/*!
 *
 * Bryntum Calendar 6.2.5
 *
 * Copyright(c) 2025 Bryntum AB
 * https://bryntum.com/contact
 * https://bryntum.com/license
 *
 * Bryntum incorporates third-party code licensed under the MIT and Apache-2.0 licenses.
 * See the licenses below or visit https://bryntum.com/products/calendar/docs/guide/Calendar/licenses
 *
 * # Third Party Notices
 * 
 * Bryntum uses the following third party libraries:
 * 
 * * [Font Awesome 6 Free](https://fontawesome.com/license/free) (MIT/SIL OFL 1.1)
 * * [Roboto font](https://github.com/google/roboto) (Apache-2.0)
 * * [Styling Cross-Browser Compatible Range Inputs with Sass](https://github.com/darlanrod/input-range-sass) (MIT)
 * * [Tree Walker polyfill (only applies to Salesforce)](https://github.com/Krinkle/dom-TreeWalker-polyfill) (MIT)
 * * [chronograph](https://github.com/bryntum/chronograph) (MIT)
 * * [later.js](https://github.com/bunkat/later) (MIT)
 * * [Monaco editor (only used in our demos)](https://microsoft.github.io/monaco-editor) (MIT)
 * * Map/Set polyfill to fix performance issues for Salesforce LWS (MIT)
 * 
 * Note: the **chronograph** and **later.js** libraries are used in Bryntum Scheduler Pro and Bryntum Gantt, but they are
 * listed for all Bryntum products since the distribution contains trial versions of the thin bundles for all other
 * products. TreeWalker is only used in the LWC bundle for Salesforce. Roboto font is only used in the material theme.
 * 
 * ## Font Awesome 6 Free
 * 
 * [Font Awesome Free 6 by @fontawesome](https://fontawesome.com/)
 * 
 * Font Awesome Free is free, open source, and GPL friendly. You can use it for commercial projects, open source projects,
 * or really almost whatever you want.
 * 
 * [Full Font Awesome Free license](https://fontawesome.com/license/free)
 * 
 * ## Roboto font
 * 
 * [Apache License Version 2.0, January 2004](https://www.apache.org/licenses/LICENSE-2.0)
 * 
 * TERMS AND CONDITIONS FOR USE, REPRODUCTION, AND DISTRIBUTION
 * 
 * 1. Definitions.
 * 
 * "License" shall mean the terms and conditions for use, reproduction, and distribution as defined by Sections 1 through 9
 * of this document.
 * 
 * "Licensor" shall mean the copyright owner or entity authorized by the copyright owner that is granting the License.
 * 
 * "Legal Entity" shall mean the union of the acting entity and all other entities that control, are controlled by, or are
 * under common control with that entity. For the purposes of this definition,
 * "control" means (i) the power, direct or indirect, to cause the direction or management of such entity, whether by
 * contract or otherwise, or (ii) ownership of fifty percent (50%) or more of the outstanding shares, or (iii) beneficial
 * ownership of such entity.
 * 
 * "You" (or "Your") shall mean an individual or Legal Entity exercising permissions granted by this License.
 * 
 * "Source" form shall mean the preferred form for making modifications, including but not limited to software source code,
 * documentation source, and configuration files.
 * 
 * "Object" form shall mean any form resulting from mechanical transformation or translation of a Source form, including
 * but not limited to compiled object code, generated documentation, and conversions to other media types.
 * 
 * "Work" shall mean the work of authorship, whether in Source or Object form, made available under the License, as
 * indicated by a copyright notice that is included in or attached to the work
 * (an example is provided in the Appendix below).
 * 
 * "Derivative Works" shall mean any work, whether in Source or Object form, that is based on (or derived from) the Work
 * and for which the editorial revisions, annotations, elaborations, or other modifications represent, as a whole, an
 * original work of authorship. For the purposes of this License, Derivative Works shall not include works that remain
 * separable from, or merely link (or bind by name) to the interfaces of, the Work and Derivative Works thereof.
 * 
 * "Contribution" shall mean any work of authorship, including the original version of the Work and any modifications or
 * additions to that Work or Derivative Works thereof, that is intentionally submitted to Licensor for inclusion in the
 * Work by the copyright owner or by an individual or Legal Entity authorized to submit on behalf of the copyright owner.
 * For the purposes of this definition, "submitted"
 * means any form of electronic, verbal, or written communication sent to the Licensor or its representatives, including
 * but not limited to communication on electronic mailing lists, source code control systems, and issue tracking systems
 * that are managed by, or on behalf of, the Licensor for the purpose of discussing and improving the Work, but excluding
 * communication that is conspicuously marked or otherwise designated in writing by the copyright owner as "Not a
 * Contribution."
 * 
 * "Contributor" shall mean Licensor and any individual or Legal Entity on behalf of whom a Contribution has been received
 * by Licensor and subsequently incorporated within the Work.
 * 
 * 2. Grant of Copyright License. Subject to the terms and conditions of this License, each Contributor hereby grants to
 *    You a perpetual, worldwide, non-exclusive, no-charge, royalty-free, irrevocable copyright license to reproduce,
 *    prepare Derivative Works of, publicly display, publicly perform, sublicense, and distribute the Work and such
 *    Derivative Works in Source or Object form.
 * 
 * 3. Grant of Patent License. Subject to the terms and conditions of this License, each Contributor hereby grants to You a
 *    perpetual, worldwide, non-exclusive, no-charge, royalty-free, irrevocable
 *    (except as stated in this section) patent license to make, have made, use, offer to sell, sell, import, and otherwise
 *    transfer the Work, where such license applies only to those patent claims licensable by such Contributor that are
 *    necessarily infringed by their Contribution(s) alone or by combination of their Contribution(s)
 *    with the Work to which such Contribution(s) was submitted. If You institute patent litigation against any entity (
 *    including a cross-claim or counterclaim in a lawsuit) alleging that the Work or a Contribution incorporated within
 *    the Work constitutes direct or contributory patent infringement, then any patent licenses granted to You under this
 *    License for that Work shall terminate as of the date such litigation is filed.
 * 
 * 4. Redistribution. You may reproduce and distribute copies of the Work or Derivative Works thereof in any medium, with
 *    or without modifications, and in Source or Object form, provided that You meet the following conditions:
 * 
 * (a) You must give any other recipients of the Work or Derivative Works a copy of this License; and
 * 
 * (b) You must cause any modified files to carry prominent notices stating that You changed the files; and
 * 
 * (c) You must retain, in the Source form of any Derivative Works that You distribute, all copyright, patent, trademark,
 * and attribution notices from the Source form of the Work, excluding those notices that do not pertain to any part of the
 * Derivative Works; and
 * 
 * (d) If the Work includes a "NOTICE" text file as part of its distribution, then any Derivative Works that You distribute
 * must include a readable copy of the attribution notices contained within such NOTICE file, excluding those notices that
 * do not pertain to any part of the Derivative Works, in at least one of the following places: within a NOTICE text file
 * distributed as part of the Derivative Works; within the Source form or documentation, if provided along with the
 * Derivative Works; or, within a display generated by the Derivative Works, if and wherever such third-party notices
 * normally appear. The contents of the NOTICE file are for informational purposes only and do not modify the License. You
 * may add Your own attribution notices within Derivative Works that You distribute, alongside or as an addendum to the
 * NOTICE text from the Work, provided that such additional attribution notices cannot be construed as modifying the
 * License.
 * 
 * You may add Your own copyright statement to Your modifications and may provide additional or different license terms and
 * conditions for use, reproduction, or distribution of Your modifications, or for any such Derivative Works as a whole,
 * provided Your use, reproduction, and distribution of the Work otherwise complies with the conditions stated in this
 * License.
 * 
 * 5. Submission of Contributions. Unless You explicitly state otherwise, any Contribution intentionally submitted for
 *    inclusion in the Work by You to the Licensor shall be under the terms and conditions of this License, without any
 *    additional terms or conditions. Notwithstanding the above, nothing herein shall supersede or modify the terms of any
 *    separate license agreement you may have executed with Licensor regarding such Contributions.
 * 
 * 6. Trademarks. This License does not grant permission to use the trade names, trademarks, service marks, or product
 *    names of the Licensor, except as required for reasonable and customary use in describing the origin of the Work and
 *    reproducing the content of the NOTICE file.
 * 
 * 7. Disclaimer of Warranty. Unless required by applicable law or agreed to in writing, Licensor provides the Work (and
 *    each Contributor provides its Contributions) on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND,
 *    either express or implied, including, without limitation, any warranties or conditions of TITLE, NON-INFRINGEMENT,
 *    MERCHANTABILITY, or FITNESS FOR A PARTICULAR PURPOSE. You are solely responsible for determining the appropriateness
 *    of using or redistributing the Work and assume any risks associated with Your exercise of permissions under this
 *    License.
 * 
 * 8. Limitation of Liability. In no event and under no legal theory, whether in tort (including negligence), contract, or
 *    otherwise, unless required by applicable law (such as deliberate and grossly negligent acts) or agreed to in writing,
 *    shall any Contributor be liable to You for damages, including any direct, indirect, special, incidental, or
 *    consequential damages of any character arising as a result of this License or out of the use or inability to use the
 *    Work (including but not limited to damages for loss of goodwill, work stoppage, computer failure or malfunction, or
 *    any and all other commercial damages or losses), even if such Contributor has been advised of the possibility of such
 *    damages.
 * 
 * 9. Accepting Warranty or Additional Liability. While redistributing the Work or Derivative Works thereof, You may choose
 *    to offer, and charge a fee for, acceptance of support, warranty, indemnity, or other liability obligations and/or
 *    rights consistent with this License. However, in accepting such obligations, You may act only on Your own behalf and
 *    on Your sole responsibility, not on behalf of any other Contributor, and only if You agree to indemnify, defend, and
 *    hold each Contributor harmless for any liability incurred by, or claims asserted against, such Contributor by reason
 *    of your accepting any such warranty or additional liability.
 * 
 * END OF TERMS AND CONDITIONS
 * 
 * APPENDIX: How to apply the Apache License to your work.
 * 
 * To apply the Apache License to your work, attach the following boilerplate notice, with the fields enclosed by
 * brackets "[]"
 * replaced with your own identifying information. (Don't include the brackets!)  The text should be enclosed in the
 * appropriate comment syntax for the file format. We also recommend that a file or class name and description of purpose
 * be included on the same "printed page" as the copyright notice for easier identification within third-party archives.
 * 
 * Copyright [yyyy] [name of copyright owner]
 * 
 * Licensed under the Apache License, Version 2.0 (the "License"); you may not use this file except in compliance with the
 * License. You may obtain a copy of the License at
 * 
 * [APACHE LICENSE, VERSION 2.0](http://www.apache.org/licenses/LICENSE-2.0)
 * 
 * Unless required by applicable law or agreed to in writing, software distributed under the License is distributed on an "
 * AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied. See the License for the specific
 * language governing permissions and limitations under the License.
 * 
 * ## Styling Cross-Browser Compatible Range Inputs with Sass
 * 
 * Github: [input-range-sass](https://github.com/darlanrod/input-range-sass)
 * 
 * Author: [Darlan Rod](https://github.com/darlanrod)
 * 
 * Version 1.4.1
 * 
 * The MIT License (MIT)
 * 
 * Copyright (c) 2016 Darlan Rod
 * 
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated
 * documentation files (the "Software"), to deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit
 * persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the
 * Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE
 * WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 * COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
 * OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 * 
 * ## Tree Walker polyfill
 * 
 * The MIT License (MIT)
 * 
 * [Copyright 2013–2017 Timo Tijhof](https://github.com/Krinkle/dom-TreeWalker-polyfill)
 * 
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated
 * documentation files (the "Software"), to deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit
 * persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the
 * Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE
 * WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 * COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
 * OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 * 
 * ## chronograph
 * 
 * GitHub: [chronograph](https://github.com/bryntum/chronograph)
 * 
 * The MIT License (MIT)
 * 
 * Copyright (c) 2023 Bryntum
 * 
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated
 * documentation files (the "Software"), to deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit
 * persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the
 * Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE
 * WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 * COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
 * OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 * 
 * ## later.js
 * 
 * GitHub: [later.js](https://github.com/bunkat/later)
 * 
 * The MIT License (MIT)
 * 
 * Copyright © 2013 BunKat
 * 
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated
 * documentation files (the "Software"), to deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit
 * persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the
 * Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE
 * WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 * COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
 * OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 * 
 * ## Monaco editor
 * 
 * GitHub: [Monaco editor](https://microsoft.github.io/monaco-editor) (MIT)
 * 
 * The MIT License (MIT)
 * 
 * Copyright (c) 2016 - present Microsoft Corporation
 * 
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated
 * documentation files (the "Software"), to deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit
 * persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the
 * Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE
 * WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 * COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
 * OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 * 
 * ## Map/Set polyfill to fix performance issues for Salesforce LWS
 * 
 * Copyright © 2024 Certinia Inc.
 * 
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 * 
 */
var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value2) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value: value2 }) : obj[key] = value2;
var __publicField = (obj, key, value2) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value2);
  return value2;
};
var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value2) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value2);
};
var __privateSet = (obj, member, value2, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value2) : member.set(obj, value2);
  return value2;
};

// ../Core/lib/Core/helper/Helpers.js
var registry = /* @__PURE__ */ Object.create(null);
var helpers = [
  // One of these things is not like the others...
  "Array",
  "Browser",
  ["Css", "CSSHelper"],
  "Date",
  "Dom",
  "Event",
  "Function",
  "Object",
  "String",
  "TimeZone",
  "Version"
].map((el) => Array.isArray(el) ? el : [el, `${el}Helper`]);
var mapify = (entries) => Object.assign(/* @__PURE__ */ Object.create(null), Object.fromEntries(entries));
var byName = mapify(helpers);
var byType = mapify(helpers.map(([k, v]) => [v, k]));
var denied = (reason) => (obj, name) => {
  throw new Error(`Cannot ${reason} "Helpers.${name}": object is readonly`);
};
var Helpers = new Proxy(registry, {
  defineProperty: denied("define property"),
  deleteProperty: denied("delete"),
  set: denied("assign"),
  get(obj, name) {
    const ret = registry[name];
    if (!ret) {
      throw new Error(`Property not defined "Helpers.${name}": ${name in byName ? `import ${byName[name]} before using it` : `invalid helper name`}`);
    }
    return ret;
  }
  // we don't need any other getter traps because we populate "registry" (the proxy target) below.
});
Object.defineProperty(registry, "register", {
  // not enumerable, configurable, writable
  value(helper) {
    const name = helper.$name, key = byType[name];
    registry[key] = helper;
  }
});
helpers.forEach((ent) => registry[ent[0]] = null);
var Helpers_default = Helpers;

// ../Core/lib/Core/helper/BrowserHelper.js
var _a;
var BrowserHelper = class {
  static cacheFlags(platform = navigator.platform, userAgent = navigator.userAgent) {
    const me = this;
    me._isLinux = Boolean(platform.match(/Linux/));
    me._isMac = Boolean(platform.match(/Mac/));
    me._isWindows = Boolean(platform.match(/Win32/));
    me._isMobile = Boolean(userAgent.match(/Mobile|Opera Mini|Opera Mobi|Puffin/) || typeof globalThis.orientation === "number");
    me._isWebkit = Boolean(userAgent.match(/WebKit/) && !userAgent.match(/Edg/));
    me._firefoxVersion = me.getVersion(userAgent, /Firefox\/(\d+)\./);
    me._isFirefox = me._firefoxVersion > 0;
    me._chromeVersion = me.getVersion(userAgent, /Chrom(?:e|ium)\/(\d+)\./);
    me._isChrome = me._chromeVersion > 0;
    me._isSafari = Boolean(userAgent.match(/Safari/)) && !me._isChrome;
    me._isMobileSafari = me._isSafari && me.isMobile;
    me._safariVersion = me.getVersion(userAgent, /Version\/(.*).Safari/);
    me._isAndroid = Boolean(userAgent.match(/Android/g));
  }
  //endregion
  //region Device
  /**
   * Yields `true` if the current browser supports CSS style `overflow:clip`.
   * @property {Boolean}
   * @readonly
   * @internal
   */
  static get supportsOverflowClip() {
    if (this._supportsOverflowClip == null) {
      const div = document.createElement("div");
      div.style.overflow = "clip";
      div.style.display = "none";
      document.documentElement.appendChild(div);
      this._supportsOverflowClip = div.ownerDocument.defaultView.getComputedStyle(div).getPropertyValue("overflow") === "clip";
      div.remove();
    }
    return this._supportsOverflowClip;
  }
  /**
   * Yields `true` if the current browser supports CSS style `position:sticky`.
   * @property {Boolean}
   * @readonly
   * @internal
   */
  static get supportsSticky() {
    return true;
  }
  /**
   * Returns matched version for userAgent.
   * @param {String} versionRe version match regular expression
   * @returns {Number} matched version
   * @readonly
   * @internal
   */
  static getVersion(userAgent, versionRe) {
    const match = userAgent.match(versionRe);
    return match ? parseFloat(match[1]) : 0;
  }
  /**
   * Determines if the user is using a touch device.
   * @property {Boolean}
   * @readonly
   * @internal
   */
  static get isTouchDevice() {
    if (this._isTouchDevice === void 0) {
      this._isTouchDevice = globalThis.matchMedia("(any-pointer:coarse)").matches;
    }
    return this._isTouchDevice;
  }
  // Reports true by default for our tests
  static get isHoverableDevice() {
    if (this._isHoverableDevice === void 0) {
      this._isHoverableDevice = globalThis.matchMedia("(any-hover: hover)").matches;
    }
    return this._isHoverableDevice;
  }
  //endregion
  //region Platform
  static get isBrowserEnv() {
    return typeof window !== "undefined";
  }
  /**
   * Checks if platform is Mac.
   * @property {Boolean}
   * @readonly
   * @category Platform
   */
  static get isMac() {
    return this._isMac;
  }
  /**
   * Checks if platform is Windows.
   * @property {Boolean}
   * @readonly
   * @category Platform
   */
  static get isWindows() {
    return this._isWindows;
  }
  /**
   * Checks if platform is Linux.
   * @property {Boolean}
   * @readonly
   * @category Platform
   */
  static get isLinux() {
    return this._isLinux;
  }
  /**
   * Checks if platform is Android.
   * @property {Boolean}
   * @readonly
   * @category Platform
   */
  static get isAndroid() {
    return this._isAndroid;
  }
  //endregion
  //region Browser
  /**
   * Checks if browser is Webkit.
   * @property {Boolean}
   * @readonly
   * @category Browser
   */
  static get isWebkit() {
    return this._isWebkit;
  }
  /**
   * Checks if browser is Chrome or Chromium based browser.
   * Returns truthy value for Edge Chromium.
   * @property {Boolean}
   * @readonly
   * @category Browser
   */
  static get isChrome() {
    return this._isChrome;
  }
  /**
   * Returns the major Chrome version or 0 for other browsers.
   * @property {Number}
   * @readonly
   * @category Browser
   */
  static get chromeVersion() {
    return this._chromeVersion;
  }
  /**
   * Checks if browser is Firefox.
   * @property {Boolean}
   * @readonly
   * @category Browser
   */
  static get isFirefox() {
    return this._isFirefox;
  }
  /**
   * Returns the major Firefox version or 0 for other browsers.
   * @property {Number}
   * @readonly
   * @category Browser
   */
  static get firefoxVersion() {
    return this._firefoxVersion;
  }
  /**
   * Checks if browser is Safari.
   * @property {Boolean}
   * @readonly
   * @category Browser
   */
  static get isSafari() {
    return this._isSafari;
  }
  static get safariVersion() {
    return this._safariVersion;
  }
  /**
   * Checks if browser is mobile Safari
   * @property {Boolean}
   * @readonly
   * @category Browser
   */
  static get isMobileSafari() {
    return this._isMobileSafari;
  }
  /**
   * Checks if the active device is a mobile device
   * @property {Boolean}
   * @readonly
   * @category Browser
   */
  static get isMobile() {
    return this._isMobile;
  }
  static get platform() {
    const me = this;
    return me._isLinux ? "linux" : me._isMac ? "mac" : me._isWindows ? "windows" : me._isAndroid ? "android" : me._isMobileSafari ? "ios" : null;
  }
  // Only works in secure contexts
  static get supportsRandomUUID() {
    var _a5, _b2;
    if (this._supportsRandomUUID === void 0) {
      try {
        this._supportsRandomUUID = Boolean(((_b2 = (_a5 = globalThis.crypto).randomUUID) == null ? void 0 : _b2.call(_a5).length) > 0);
      } catch (e) {
        this._supportsRandomUUID = false;
      }
    }
    return this._supportsRandomUUID;
  }
  //endregion
  //region Storage
  // https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API
  static get storageAvailable() {
    let storage, x;
    try {
      storage = localStorage;
      x = "__storage_test__";
      storage.setItem(x, x);
      storage.removeItem(x);
      return true;
    } catch (e) {
      return e instanceof DOMException && // everything except Firefox
      (e.code === 22 || // Firefox
      e.code === 1014 || // test name field too, because code might not be present
      // everything except Firefox
      e.name === "QuotaExceededError" || // Firefox
      e.name === "NS_ERROR_DOM_QUOTA_REACHED") && // acknowledge QuotaExceededError only if there's something already stored
      storage.length !== 0;
    }
  }
  static setLocalStorageItem(key, value2) {
    this.storageAvailable && localStorage.setItem(key, value2);
  }
  static getLocalStorageItem(key) {
    return this.storageAvailable && localStorage.getItem(key);
  }
  static removeLocalStorageItem(key) {
    this.storageAvailable && localStorage.removeItem(key);
  }
  //endregion
  //region Helpers
  /**
   * Returns the event property name for the `ctrlKey`. The `ctrlKey` key is `'ctrlKey'` on all platforms except Mac,
   * where `ctrlKey` is `'metaKey'`.
   *
   * See also {@link #property-metaKey-static}.
   * @property {String}
   * @readonly
   * @category Browser
   * @internal
   */
  static get ctrlKey() {
    return this.isMac ? "metaKey" : "ctrlKey";
  }
  /**
   * Returns the event property name for the `metaKey`. The `metaKey` is `'metaKey'` on all platforms except Mac,
   * where `metaKey` is `'ctrlKey'`.
   *
   * See also {@link #property-ctrlKey-static}.
   * @property {String}
   * @readonly
   * @category Browser
   * @internal
   */
  static get metaKey() {
    return this.isMac ? "ctrlKey" : "metaKey";
  }
  /**
   * Returns parameter value from search string by parameter name.
   * @param {String} paramName search parameter name
   * @param {String} [defaultValue] default value if parameter not found
   * @param {String} [search] search string. Defaults to `document.location.search`
   * @returns {String} search parameter string value
   * @category Helper
   */
  static searchParam(paramName, defaultValue2 = null, search = document.location.search) {
    const re = new RegExp(`[?&]${paramName}=?([^&]*)`), match = search.match(re);
    return match && match[1] || defaultValue2;
  }
  /**
   * Returns cookie by name.
   * @param {String} name cookie name
   * @returns {String} cookie string value
   * @category Helper
   */
  static getCookie(name) {
    const nameEq = encodeURIComponent(name) + "=", cookieItems = document.cookie.split(";");
    for (let i = 0; i < cookieItems.length; i++) {
      let c = cookieItems[i];
      while (c.charAt(0) === " ") {
        c = c.substring(1, c.length);
      }
      if (c.indexOf(nameEq) === 0) {
        return decodeURIComponent(c.substring(nameEq.length, c.length));
      }
    }
    return "";
  }
  /**
   * Triggers a download of a file with the specified name / URL.
   * @param {String} filename The filename of the file to be downloaded
   * @param {String} [url] The URL where the file is to be downloaded from
   * @internal
   * @category Download
   */
  static download(filename, url) {
    const a = document.createElement("a");
    a.download = filename;
    a.href = url || filename;
    a.style.cssText = "display:none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
  /**
   * Triggers a download of a Blob with the specified name.
   * @param {Blob} blob The Blob to be downloaded
   * @param {String} filename The filename of the file to be downloaded
   * @internal
   * @category Download
   */
  static downloadBlob(blob, filename) {
    const url = globalThis.URL.createObjectURL(blob);
    this.download(filename, url);
    globalThis.URL.revokeObjectURL(url);
  }
  static get queryString() {
    var _a5;
    const params = new URL(globalThis.location.href).searchParams;
    return (_a5 = Object.fromEntries) == null ? void 0 : _a5.call(Object, params.entries());
  }
  // Used by docs fiddle
  static copyToClipboard(code) {
    let success2 = true;
    const textArea = document.createElement("textarea");
    textArea.value = code;
    textArea.style.height = textArea.style.width = 0;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand("copy");
    } catch (e) {
      success2 = false;
    }
    textArea.remove();
    return success2;
  }
  static isBryntumOnline(searchStrings) {
    searchStrings = Array.isArray(searchStrings) ? searchStrings : [searchStrings];
    return Boolean(/^(www\.)?bryntum\.com$/.test(globalThis.location.host) || (searchStrings == null ? void 0 : searchStrings.some((str) => this.queryString[str] != null)));
  }
  /**
   * Returns truthy value if page contains Content Security Policy meta tag or globalThis.bryntum.CSP is truthy value
   * @returns {Boolean}
   * @internal
   **/
  static get isCSP() {
    const { bryntum: bryntum2, document: document2 } = globalThis;
    if (bryntum2.CSP == null) {
      bryntum2.CSP = Boolean(document2.querySelector('meta[http-equiv="Content-Security-Policy"]'));
    }
    return bryntum2.CSP;
  }
  //endregion
};
__publicField(BrowserHelper, "$name", "BrowserHelper");
__publicField(BrowserHelper, "supportsPointerEvents", Boolean(globalThis.PointerEvent || globalThis.MSPointerEvent));
// Locker Service does not allow to instantiate PointerEvents. LWS apparently does, however.
// https://github.com/bryntum/support/issues/5578
__publicField(BrowserHelper, "supportsPointerEventConstructor", typeof PointerEvent !== "undefined");
__publicField(BrowserHelper, "PointerEventConstructor", globalThis.PointerEvent || globalThis.CustomEvent);
//region Init
/**
 * Yields `true` if the platform running is a phone (screen width or height <= 414 CSS pixels)
 * @property {Boolean}
 * @readonly
 * @static
 * @category Platform
 */
__publicField(BrowserHelper, "isPhone", (_a = globalThis.matchMedia) == null ? void 0 : _a.call(globalThis, "(max-height:414px) or (max-width:414px)").matches);
Helpers_default.register(BrowserHelper);
if (BrowserHelper.isBrowserEnv) {
  BrowserHelper.cacheFlags();
}
BrowserHelper._$name = "BrowserHelper";

// ../Core/lib/Core/helper/util/Objects.js
var { hasOwnProperty: hasOwnProperty2, propertyIsEnumerable, toString } = Object.prototype;
var { isFrozen } = Object;
var afterRe = /\s*<\s*/;
var beforeRe = /\s*>\s*/;
var blendOptions = {};
var typeCache = {};
var emptyObject = Object.freeze({});
var Objects = class _Objects {
  static assign(dest, ...sources) {
    for (let source, key, i = 0; i < sources.length; i++) {
      source = sources[i];
      if (source) {
        for (key in source) {
          if (key !== "__proto__" && key !== "constructor") {
            dest[key] = source[key];
          }
        }
      }
    }
    return dest;
  }
  static assignIf(dest, ...sources) {
    for (let source, key, i = 0; i < sources.length; i++) {
      source = sources[i];
      if (source) {
        for (key in source) {
          if (key !== "__proto__" && key !== "constructor") {
            if (!(key in dest) || dest[key] === void 0) {
              dest[key] = source[key];
            }
          }
        }
      }
    }
    return dest;
  }
  static blend(dest, source, options) {
    options = options || blendOptions;
    dest = dest || {};
    const { clone = _Objects.clone, merge = _Objects.blend } = options;
    if (Array.isArray(source)) {
      if (source.length > 1) {
        source.forEach((s) => {
          dest = _Objects.blend(dest, s, options);
        });
        return dest;
      }
      source = source[0];
    }
    if (source) {
      let destValue, key, value2;
      for (key in source) {
        if (key !== "__proto__" && key !== "constructor") {
          value2 = source[key];
          if (value2 && _Objects.isObject(value2)) {
            destValue = dest[key];
            options.key = key;
            if (destValue && _Objects.isObject(destValue)) {
              if (isFrozen(destValue)) {
                dest[key] = destValue = clone(destValue, options);
              }
              value2 = merge(destValue, value2, options);
            } else {
              value2 = isFrozen(value2) ? value2 : clone(value2, options);
            }
          }
          dest[key] = value2;
        }
      }
    }
    return dest;
  }
  static clone(value2, handler) {
    let cloned = value2, key;
    if (value2 && typeof value2 === "object" && !(value2 instanceof Event)) {
      const options = handler && typeof handler === "object" && handler;
      if (options) {
        handler = null;
      }
      if (_Objects.isObject(value2)) {
        if (value2.skipClone) {
          cloned = value2;
        } else {
          cloned = {};
          for (key in value2) {
            if (key !== "__proto__" && key !== "constructor") {
              cloned[key] = _Objects.clone(value2[key]);
            }
          }
        }
      } else if (Array.isArray(value2)) {
        cloned = [];
        for (key = value2.length; key-- > 0; ) {
          cloned[key] = _Objects.clone(value2[key]);
        }
      } else if (_Objects.isDate(value2)) {
        cloned = new Date(value2.getTime());
      } else if (handler) {
        cloned = handler(value2);
      }
    }
    return cloned;
  }
  static mergeUniformObjects(dest, ...sources) {
    for (const source of sources) {
      for (const key in source) {
        if (key !== "__proto__" && key !== "constructor") {
          const value2 = source[key];
          if (value2) {
            if (this.isObject(value2)) {
              this.mergeUniformObjects(dest[key] || (dest[key] = {}), value2);
            } else if (Array.isArray(value2)) {
              if (!(key in dest)) {
                dest[key] = [];
              }
              this.mergeArrayOfIdentityObjects(dest[key], value2);
            } else {
              dest[key] = this.clone(value2);
            }
          }
        }
      }
    }
    return dest;
  }
  static mergeArrayOfIdentityObjects(dest, sources) {
    for (const source of sources) {
      if (this.isObject(source)) {
        const existing = dest.find((item) => item.id === source.id);
        if (existing) {
          this.mergeUniformObjects(existing, source);
        } else {
          dest.push(this.clone(source));
        }
      } else {
        dest.push(source);
      }
    }
  }
  static createTruthyKeys(source) {
    const keys = StringHelper.split(source), result = keys && {};
    if (keys) {
      for (const key of keys) {
        if (key) {
          result[key] = true;
        }
      }
    }
    return result;
  }
  /**
   * Returns value for a given path in the object
   * @param {Object} object Object to check path on
   * @param {String} path Dot-separated path, e.g. 'object.childObject.someKey'
   * @returns {*} Value associated with passed key
   */
  static getPath(object, path) {
    return path.split(".").reduce((result, key) => {
      return (result || emptyObject)[key];
    }, object);
  }
  /**
   * Returns value for a given path in the object, placing a passed default value in at the
   * leaf property and filling in undefined properties all the way down.
   * @param {Object} object Object to get path value for.
   * @param {String|Number|String[]|Number[]} path Dot-separated path, e.g. 'firstChild.childObject.someKey',
   * or the key path as an array, e.g. ['firstChild', 'childObject', 'someKey'].
   * @param {*} [defaultValue] Optionally the value to put in as the `someKey` property.
   * @returns {*} Value at the leaf position of the path.
   */
  static getPathDefault(object, path, defaultValue2) {
    const keys = Array.isArray(path) ? path : typeof path === "string" ? path.split(".") : [path], length = keys.length - 1;
    return keys.reduce((result, key, index) => {
      if (defaultValue2 && !(key in result)) {
        result[key] = index === length ? defaultValue2 : {};
      }
      return (result || emptyObject)[key];
    }, object);
  }
  /**
   * Finds a property descriptor for the passed object from all inheritance levels.
   * @param {Object} object The Object whose property to find.
   * @param {String} propertyName The name of the property to find.
   * @returns {Object} An ECMA property descriptor is the property was found, otherwise `null`
   */
  static getPropertyDescriptor(object, propertyName) {
    let result = null;
    for (let o = object; o && !result && !_Objects.hasOwn(o, "isBase"); o = Object.getPrototypeOf(o)) {
      result = Object.getOwnPropertyDescriptor(o, propertyName);
    }
    return result;
  }
  /**
   * Determines if the specified path exists
   * @param {Object} object Object to check path on
   * @param {String} path Dot-separated path, e.g. 'object.childObject.someKey'
   * @returns {Boolean}
   */
  static hasPath(object, path) {
    return path.split(".").every((key) => {
      if (object && key in object) {
        object = object[key];
        return true;
      }
      return false;
    });
  }
  static getTruthyKeys(source) {
    let keys;
    if (typeof source === "string") {
      keys = [source];
    } else if (Array.isArray(source)) {
      keys = source.slice();
    } else {
      keys = [];
      if (source) {
        for (const key in source) {
          if (source[key]) {
            keys.push(key);
          }
        }
      }
    }
    return keys;
  }
  static getTruthyValues(source) {
    const values = [];
    if (source) {
      for (const key in source) {
        if (source[key]) {
          values.push(source[key]);
        }
      }
    }
    return values;
  }
  static isClass(object) {
    var _a5;
    if (typeof object === "function" && ((_a5 = object.prototype) == null ? void 0 : _a5.constructor) === object) {
      return true;
    }
    return false;
  }
  static isDate(object) {
    return Boolean(object == null ? void 0 : object.getUTCDate) && _Objects.typeOf(object) === "date";
  }
  /**
   * Check if passed object is a Promise or contains `then` method.
   * Used to fix problems with detecting promises in code with `instance of Promise` when
   * Promise class is replaced with any other implementation like `ZoneAwarePromise` in Angular.
   * Related to these issues:
   * https://github.com/bryntum/support/issues/791
   * https://github.com/bryntum/support/issues/2990
   *
   * @param {Object} object object to check
   * @returns {Boolean} truthy value if object is a Promise
   * @internal
   */
  static isPromise(object) {
    if (Promise == null ? void 0 : Promise.resolve) {
      return Promise.resolve(object) === object || typeof (object == null ? void 0 : object.then) === "function";
    }
    throw new Error("Promise not supported in your environment");
  }
  static isEmpty(object) {
    if (object && typeof object === "object") {
      for (const p in object) {
        return false;
      }
    }
    return true;
  }
  static isObject(value2) {
    const C = value2 == null ? void 0 : value2.constructor;
    return Boolean(
      C && // most things have a .constructor property, but...
      typeof C === "function" && !_Objects.hasOwn(value2, "constructor") && // ... true constructors are only on prototypes
      !propertyIsEnumerable.call(value2, "constructor") ? (
        // An in-frame instance of Object
        (C === Object || // Detect cross-frame objects, but exclude instance of custom classes named Object. typeOf(value) is
        // "object" even for instances of a class and typeOf(C) is "function" for all constructors. We'll have
        // to settle for relying on the fact that getPrototypeOf(Object.prototype) === null.
        // NOTE: this issue does come up in Scheduler unit tests at least.
        C.getPrototypeOf && C.prototype && !Object.getPrototypeOf(C.prototype)) && // Make sure it isn't some testing tools replacement for globalThis (vitest turns globalThis into an object)
        value2 !== globalThis
      ) : value2 && typeof value2 === "object"
    );
  }
  static isInstantiated(object) {
    return object ? typeof object === "object" && !_Objects.isObject(object) : false;
  }
  static merge(dest, ...sources) {
    return _Objects.blend(dest, sources);
  }
  /**
   * Merges two "items" objects. An items object is a simple object whose keys act as identifiers and whose values
   * are "item" objects. An item can be any object type. This method is used to merge such objects while maintaining
   * their property order. Special key syntax is used to allow a source object to insert a key before or after a key
   * in the `dest` object.
   *
   * For example:
   * ```javascript
   *  let dest = {
   *      foo : {},
   *      bar : {},
   *      fiz : {}
   *  }
   *
   *  console.log(Object.keys(dest));
   *  > ["foo", "bar", "fiz"]
   *
   *  dest = mergeItems(dest, {
   *      'zip > bar' : {}    // insert "zip" before "bar"
   *      'bar < zap' : {}    // insert "zap" after "bar"
   *  });
   *
   *  console.log(Object.keys(dest));
   *  > ["foo", "zip", "bar", "zap", "fiz"]
   * ```
   *
   * @param {Object} dest The destination object.
   * @param {Object|Object[]} src The source object or array of source objects to merge into `dest`.
   * @param {Object} [options] The function to use to merge items.
   * @param {Function} [options.merge] The function to use to merge items.
   * @returns {Object} The merged object. This will be the `dest` object.
   * @internal
   */
  static mergeItems(dest, src, options) {
    options = options || blendOptions;
    let anchor, delta, index, indexMap, key, shuffle, srcVal;
    const { merge = _Objects.blend } = options;
    dest = dest || {};
    if (Array.isArray(src)) {
      src.forEach((s) => {
        dest = _Objects.mergeItems(dest, s, options);
      });
    } else if (src) {
      for (key in src) {
        if (key !== "__proto__" && key !== "constructor") {
          srcVal = src[key];
          anchor = null;
          if (key.includes(">")) {
            [key, anchor] = key.split(beforeRe);
            delta = 0;
          } else if (key.includes("<")) {
            [anchor, key] = key.split(afterRe);
            delta = 1;
          }
          if (key in dest) {
            if (srcVal && dest[key] && merge) {
              options.key = key;
              srcVal = merge(dest[key], srcVal, options);
            }
            dest[key] = srcVal;
          } else if (!anchor) {
            dest[key] = srcVal;
            indexMap == null ? void 0 : indexMap.set(key, indexMap.size);
          } else {
            if (!indexMap) {
              indexMap = /* @__PURE__ */ new Map();
              index = 0;
              for (const k in dest) {
                indexMap.set(k, index++);
              }
            }
            index = indexMap.get(anchor);
            dest[key] = srcVal;
            if (index == null && delta) {
              index = indexMap.size;
            } else {
              shuffle = shuffle || [];
              index = (index || 0) + delta;
              for (const item of indexMap) {
                const [k, v] = item;
                if (index <= v) {
                  shuffle && (shuffle[indexMap.size - v - 1] = k);
                  indexMap.set(k, v + 1);
                }
              }
              if (shuffle) {
                while (shuffle.length) {
                  const k = shuffle.pop(), v = dest[k];
                  delete dest[k];
                  dest[k] = v;
                }
              }
            }
            indexMap.set(key, index);
          }
        }
      }
    }
    return dest;
  }
  /**
   * Sets value for a given path in the object
   * @param {Object} object Target object
   * @param {String} path Dot-separated path, e.g. 'object.childObject.someKey'
   * @param {*} value Value for a given path
   * @returns {Object} Returns passed object
   */
  static setPath(object, path, value2) {
    path.split(".").reduce((result, key, index, array) => {
      const isLast = index === array.length - 1;
      if (isLast) {
        return result[key] = value2;
      } else if (!(result[key] instanceof Object)) {
        result[key] = {};
      }
      return result[key];
    }, object);
    return object;
  }
  static typeOf(value2) {
    let trueType, type;
    if (value2 === null) {
      type = "null";
    } else if (value2 !== value2) {
      type = "nan";
    } else {
      type = typeof value2;
      if (type === "object") {
        if (value2.isBase) {
          type = "instance";
        } else if (Array.isArray(value2)) {
          type = "array";
        } else if (!(type = typeCache[trueType = toString.call(value2)])) {
          typeCache[trueType] = type = trueType.slice(8, -1).toLowerCase();
        }
      } else if (type === "function" && value2.isBase) {
        type = "class";
      }
    }
    return type;
  }
};
Object.defineProperty(Objects, "hasOwn", {
  // When available, this avoids an extra layer of function call around it:
  value: Object.hasOwn || ((object, property) => hasOwnProperty2.call(object, property))
});
Objects._$name = "Objects";

// ../Core/lib/Core/helper/StringHelper.js
var charsToEncode;
var entitiesToDecode;
var htmlEncodeRe;
var htmlDecodeRe;
var camelLettersRe = /([a-z])([A-Z])/g;
var crlfRe = /[\n\r]/g;
var escapeRegExpRe = /[.*+?^${}()|[\]\\]/g;
var htmlRe = /[&<]/;
var idRe = /(^[^a-z]+[^\w]+)/gi;
var whiteSpaceRe = /\s+/;
var domIdRe = /^[^a-z]+|[^\w:.-]+/gi;
var htmlDecoder = (m, captured) => entitiesToDecode[captured.toLowerCase()] || String.fromCharCode(parseInt(captured.substr(2), 10));
var htmlEncoder = (m, captured) => charsToEncode[captured];
var hyphenateCamelLetters = (all, g1, g2) => {
  return `${g1}-${g2.toLowerCase()}`;
};
var separateCamelLetters = (all, g1, g2) => {
  return `${g1} ${g2.toLowerCase()}`;
};
var replaceNonIdChar = (c) => {
  if (c) {
    return `_x${[...c].map((ch) => ch.charCodeAt(0).toString(16)).join("")}`;
  }
  return "__blank__";
};
var hyphenateCache = {};
var separatedCache = {};
var ancestors = [];
var _StringHelper = class _StringHelper {
  //region Transform
  static circularReplacer(key, value2) {
    if (value2 == null ? void 0 : value2.isBase) {
      return value2.id;
    }
    if (value2 && typeof value2 === "object") {
      while (ancestors.length && ancestors[ancestors.length - 1] !== this) {
        ancestors.pop();
      }
      if (ancestors.includes(value2)) {
        return "[Circular]";
      }
      ancestors.push(value2);
    }
    return value2;
  }
  /**
   * Capitalizes the first letter of a string, "myString" -> "MyString".
   * @param {String} string The string to capitalize
   * @returns {String} The capitalized string or the value of `string` if falsy.
   * @category String formatting
   */
  static capitalize(string2) {
    return string2 && string2[0].toUpperCase() + string2.substr(1);
  }
  /**
   * Makes the first letter of a string lowercase, "MyString" -> "myString".
   * @param {String} string The string to un-capitalize.
   * @returns {String} The un-capitalized string or the value of `string` if falsy.
   * @category String formatting
   */
  static uncapitalize(string2) {
    return string2 && string2[0].toLowerCase() + string2.substr(1);
  }
  /**
   * Converts the passed camelCased string to a hyphen-separated string. eg "minWidth" -> "min-width"
   * @param {String} string The string to convert.
   * @returns {String} The string with adjoining lower and upper case letters
   * separated by hyphens and converted to lower case.
   * @category String formatting
   * @internal
   */
  static hyphenate(string2) {
    const cached = hyphenateCache[string2];
    if (cached) {
      return cached;
    }
    return hyphenateCache[string2] = string2.replace(camelLettersRe, hyphenateCamelLetters);
  }
  /**
   * Converts the passed camelCased string to a hyphen-separated attribute name when it has a recognized prefix.
   * Use for e.g. `dataUserId` -> `data-user-id` or `ariaDescribedBy` -> `aria-described-by`.
   * @param {String} string The string to convert.
   * @returns {String} The string with adjoining lower and upper case letters
   * separated by hyphens and converted to lower case, or the unmodified input.
   * @category String formatting
   * @internal
   */
  static hyphenateAttributeName(string2) {
    return /^(data|aria)[A-Z]/.test(string2) ? string2.replace(/^(data|aria)([A-Z])/, (_, prefix, firstChar) => `${prefix}-${firstChar}`).replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase() : string2;
  }
  /**
   * Converts the passed camelCased string to a capitalized, space-separated string. eg "startDate" -> "Start date".
   * @param {String} string The string to convert.
   * @returns {String} The string with spaces separating words.
   * @category String formatting
   * @internal
   */
  static separate(string2) {
    const cached = separatedCache[string2];
    if (cached) {
      return cached;
    }
    return separatedCache[string2] = this.capitalize(string2.replace(camelLettersRe, separateCamelLetters));
  }
  /**
   * Creates an alphanumeric identifier from any passed string. Encodes spaces and non-alpha characters.
   * @param {String} inString The string from which to strip non-identifier characters.
   * @returns {String}
   * @category Misc
   * @internal
   */
  static createId(inString) {
    return String(inString).replace(idRe, replaceNonIdChar);
  }
  static makeValidDomId(id2, replaceValue = "") {
    if (id2 == null) {
      return null;
    }
    return String(id2).replace(domIdRe, replaceValue);
  }
  //endregion
  //region Html
  /**
   * Escapes special RegExp characters.
   * @param {String} string String to escape
   * @privateparam {String} [flags] Optional flags
   * @returns {String} Escaped string
   */
  static escapeRegExp(string2, flags) {
    let ret = string2.replace(escapeRegExpRe, "\\$&");
    if (flags !== void 0) {
      ret = new RegExp(ret, flags);
    }
    return ret;
  }
  /**
   * This method decodes HTML entities and returns the original HTML.
   *
   * See also {@link #function-encodeHtml-static}.
   * @param {String} str
   * @returns {String}
   * @category HTML
   */
  static decodeHtml(str) {
    return str && String(str).replace(htmlDecodeRe, htmlDecoder);
  }
  /**
   * This method encodes HTML entities and returns a string that can be placed in the document and produce the
   * original text rather than be interpreted as HTML. Using this method with user-entered values prevents those
   * values from executing as HTML (i.e., a cross-site scripting or "XSS" security issue).
   *
   * See also {@link #function-decodeHtml-static}.
   * @param {String|Number} str
   * @returns {String}
   * @category HTML
   */
  static encodeHtml(str = "") {
    return str && String(str).replace(htmlEncodeRe, htmlEncoder);
  }
  /**
   * This method is similar to {@link #function-encodeHtml-static} except that `\n` and `\r` characters in the
   * given `str` are replaced by `<br>` tags _after_ first being encoded by {@link #function-encodeHtml-static}.
   * @param {String} str
   * @returns {String}
   * @category HTML
   * @internal
   */
  static encodeHtmlBR(str) {
    var _a5;
    return (_a5 = _StringHelper.encodeHtml(str)) == null ? void 0 : _a5.replace(crlfRe, "<br>");
  }
  /**
   * Returns `true` if the provided `text` contains special HTML characters.
   * @param {String} text
   * @returns {Boolean}
   * @category HTML
   * @internal
   */
  static isHtml(text) {
    return typeof text === "string" && htmlRe.test(text || "");
  }
  /**
   * Initializes HTML entities used by {@link #function-encodeHtml-static} and {@link #function-decodeHtml-static}.
   * @param {Object} [mappings] An object whose keys are characters that should be encoded and values are the HTML
   * entity for the character.
   * @private
   */
  static initHtmlEntities(mappings) {
    mappings = mappings || {
      "&": "&amp;",
      ">": "&gt;",
      "<": "&lt;",
      '"': "&quot;",
      "'": "&#39;"
    };
    const chars = Object.keys(mappings);
    charsToEncode = mappings;
    entitiesToDecode = chars.reduce((prev, val) => {
      prev[mappings[val]] = val;
      return prev;
    }, {});
    htmlEncodeRe = new RegExp(`([${chars.map((c) => "[-]".includes(c) ? "\\" + c : c).join("")}])`, "g");
    htmlDecodeRe = new RegExp(`(${Object.values(mappings).join("|")}|&#[0-9]+;)`, "ig");
  }
  //endregion
  //region JSON
  /**
   * Parses JSON inside a try-catch block. Returns null if the string could not be parsed.
   *
   * @param {String} string String to parse
   * @returns {Object} Resulting object or `null` if parse failed
   * @category JSON
   */
  static safeJsonParse(string2) {
    let parsed = null;
    try {
      parsed = JSON.parse(string2);
    } catch (e) {
    }
    return parsed;
  }
  /**
   * Stringifies an object inside a try-catch block. Returns null if an exception is encountered.
   *
   * See [JSON.stringify on MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/stringify)
   * for more information on the arguments.
   *
   * @param {Object} object The object to stringify
   * @param {Function|'circular'|String[]|Number[]} [replacer] A function or array of string/number used to determine
   * properties to include in the JSON string. Also accepts the string 'circular' to use a built-in replacer handling
   * circular references, by replacing them with `'[Circular]'`.
   * See [MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Errors/Cyclic_object_value) for more
   * information.
   * @param {String|Number} [space] Number of spaces to indent or string used as whitespace
   * @returns {String} Resulting object or null if stringify failed
   * @category JSON
   */
  static safeJsonStringify(object, replacer = null, space = null) {
    let result = null;
    try {
      if (replacer === "circular") {
        ancestors.length = 0;
        replacer = this.circularReplacer;
      }
      result = JSON.stringify(object, replacer, space);
    } catch (e) {
    }
    return result;
  }
  //endregion
  //region Split & join
  /**
   * Joins all given paths together using the separator as a delimiter and normalizes the resulting path.
   * @param paths {Array} array of paths to join
   * @param pathSeparator [{String}] path separator. Default value is '/'
   * @returns {String}
   * @category Misc
   * @internal
   */
  static joinPaths(paths, pathSeparator = "/") {
    return paths.join(pathSeparator).replace(new RegExp("\\" + pathSeparator + "+", "g"), pathSeparator);
  }
  /**
   * Returns the provided string split on whitespace. If the string is empty or consists of only whitespace, the
   * returned array will be empty. If `str` is not a string, it is simply returned. This allows `null` or already
   * split strings (arrays) to be passed through.
   *
   * For example:
   * ```
   *  console.log(StringHelper.split(' abc def xyz   '));
   *  > ['abc', 'def', 'xyz']
   *  console.log(StringHelper.split(''));
   *  > []
   * ```
   * Compare to the standard `split()` method:
   * ```
   *  console.log(' abc def xyz   '.split(/\s+/));
   *  > ['', 'abc', 'def', 'xyz', '']
   *  console.log(''.split(/\s+/));
   *  > ['']
   * ```
   * @param {String} str
   * @param {String|RegExp} delimiter
   * @returns {String[]}
   * @category Misc
   * @internal
   */
  static split(str, delimiter = whiteSpaceRe) {
    let ret = str;
    if (typeof ret === "string") {
      ret = str.trim();
      ret = ret ? ret.split(delimiter) : [];
    }
    return ret;
  }
  //endregion
  //region XSS
  /**
   * This is a tagged template function that performs HTML encoding on replacement values to avoid XSS (Cross-Site
   * Scripting) attacks.
   *
   * For example:
   *
   * ```javascript
   *  eventRenderer(eventRecord) {
   *      return StringHelper.xss`<span class="${eventRecord.attrib}">${eventRecord.name}</span>`;
   *  }
   * ```
   *
   * @param {TemplateStringsArray} strings The template string array
   * @param {...any} values The interpolated values in the template string
   * @returns {String} The encoded string
   * See {@link Core.helper.StringHelper#function-encodeHtml-static}.
   */
  static xss(strings, ...values) {
    const buf = [];
    let i = values.length;
    buf[i] = strings[i];
    while (i-- > 0) {
      buf[i] = strings[i] + _StringHelper.encodeHtml(values[i]);
    }
    return buf.join("");
  }
  /**
   * This is a tagged template function that performs HTML encoding on replacement values to avoid XSS (Cross-Site
   * Scripting) attacks. Unlike {@link Core.helper.StringHelper#function-xss-static}, this method converts `\n` and
   * `\r` characters into `<br>` tags.
   *
   * For example:
   *
   * ```javascript
   *  eventRenderer(eventRecord) {
   *      return StringHelper.xssBR`<span class="${eventRecord.attrib}">${eventRecord.name}</span>`;
   *  }
   * ```
   *
   * See {@link Core.helper.StringHelper#function-encodeHtmlBR-static}.
   * @internal
   */
  static xssBR(strings, ...values) {
    const buf = [];
    let i = values.length;
    buf[i] = strings[i];
    while (i-- > 0) {
      buf[i] = strings[i] + _StringHelper.encodeHtmlBR(values[i]);
    }
    return buf.join("");
  }
  //endregion
  //region JavaScript string
  /**
   * Converts a value to a JavaScript string (not JSON).
   *
   * For example a date to `"new Date(y, m, d)"`, an array to `"[...]"` etc.
   *
   * @param {*} value
   * @param {Object} [options]
   * @returns {String}
   * @internal
   */
  static toJavaScriptValue(value2, options) {
    const type = Objects.typeOf(value2);
    if (type === "boolean" || type === "string" || type === "number" || value2 === null) {
      return _StringHelper.safeJsonStringify(value2);
    }
    if (value2 === globalThis) {
      return "window";
    }
    if (type === "date") {
      return `new Date(${value2.getFullYear()}, ${value2.getMonth()}, ${value2.getDate()}, ${value2.getHours()}, ${value2.getMinutes()}, ${value2.getSeconds()}, ${value2.getMilliseconds()})`;
    }
    if (type === "array") {
      return `[${value2.map((v) => _StringHelper.toJavaScriptValue(v, options))}]`;
    }
    if (type === "object" || type === "instance") {
      return this.toJavaScriptString(value2, options);
    }
    if (type === "function") {
      let contents = value2.toString();
      if (contents.match(/^async (\w+?)\(/)) {
        contents = contents.replace(/^async (\w+?)\(/, "async function(");
      } else if (!contents.startsWith("async(") && contents.match(/^(\w+?)\(/)) {
        contents = contents.replace(/^(\w+?)\(/, "function(");
      }
      return contents;
    }
    if (type === "class") {
      if (value2.toJavaScriptValue) {
        return value2.toJavaScriptValue(options);
      }
      return Object.prototype.hasOwnProperty.call(value2, "$name") ? value2.$name : value2.name;
    }
  }
  /**
   * Converts an object into a JavaScript string (not JSON).
   *
   * For example `{ a: 1, b: [2, 3] }` -> `"'{ a: 1, b: [2, 3] }'"`
   *
   * @param {Object} obj
   * @param {Object} [options]
   * @returns {String}
   * @internal
   */
  static toJavaScriptString(obj, options = {}) {
    var _a5;
    const level = (_a5 = options.level) != null ? _a5 : 0, intendSize = 2;
    return "{\n" + Object.keys(obj).map(
      (key) => (
        // All properties in an object are indented one step further than the object itself
        " ".repeat((level + 1) * intendSize) + (key.match(/[- *]/) ? `"${key}"` : key) + `: ${_StringHelper.toJavaScriptValue(obj[key], { ...options, level: level + 1 })}`
      )
    ).join(",\n") + // Closing brace is indented to same level as the object
    "\n" + " ".repeat(level * intendSize) + "}";
  }
  /**
   * Escapes " and \ in CSS attribute selectors, e.g. [data-id="somevalue"]
   *
   * Usage:
   * ```javascript
   * document.querySelector(StringHelper.cssAttributeQuery('data-id', 'id with & \\ chars'))
   * ```
   *
   * @param {String} attr
   * @param {String|Number} value
   * @returns {String}
   */
  static encodeAttributeSelector(attr, value2) {
    return `[${attr}="${String(value2).replace(/["\\]/g, "\\$&")}"]`;
  }
  /**
   * Generates a UUID. Uses `Crypto.randomUUID()` if available, otherwise generates a random UUID using
   * `Crypto.getRandomValues()`.
   *
   * @returns {String}
   */
  static generateUUID() {
    var _a5;
    if (BrowserHelper.supportsRandomUUID) {
      return globalThis.crypto.randomUUID();
    }
    if ((_a5 = globalThis.crypto) == null ? void 0 : _a5.getRandomValues) {
      return ("10000000-1000-4000-8000" + -1e11).replace(
        /[018]/g,
        (c) => (c ^ globalThis.crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
      );
    }
    return `${Date.now()}-${++_StringHelper.fakeNodeUUIDIndex}`;
  }
  //endregion
};
__publicField(_StringHelper, "$name", "StringHelper");
//endregion
//region UUID
__publicField(_StringHelper, "fakeNodeUUIDIndex", 0);
var StringHelper = _StringHelper;
StringHelper.initHtmlEntities();
Helpers_default.register(StringHelper);
StringHelper._$name = "StringHelper";

// ../Core/lib/Core/helper/ObjectHelper.js
var EMPTY_ARRAY = Object.freeze([]);
var { hasOwn } = Objects;
var firstOfAsync = async (iter, defaultValue2) => {
  let ret = defaultValue2;
  for await (ret of iter) {
    break;
  }
  return ret;
};
var toFixedFix = 1.005.toFixed(2) === "1.01" ? null : (number, fractionDigits) => {
  const split2 = number.toString().split("."), newNumber = +(!split2[1] ? split2[0] : split2.join(".") + "1");
  return number.toFixed.call(newNumber, fractionDigits);
};
var _ObjectHelper = class _ObjectHelper extends Objects {
  // These methods are inherited from Objects (an internal class) but need to be documented here for public use.
  // This is primarily because static methods, while inherited by JavaScript classes, are not displayed in derived
  // classes in the docs.
  /**
   * Copies all enumerable properties from the supplied source objects to `dest`. Unlike `Object.assign`, this copy
   * also includes inherited properties.
   * @param {Object} dest The destination object.
   * @param {...Object} sources The source objects.
   * @returns {Object} The `dest` object.
   * @method assign
   * @static
   */
  /**
   * Copies all enumerable properties from the supplied source objects to `dest`, only including properties that does
   * not already exist on `dest`. Unlike `Object.assign`, this copy also includes inherited properties.
   * @param {Object} dest The destination object.
   * @param {...Object} sources The source objects.
   * @returns {Object} The `dest` object.
   * @method assignIf
   * @static
   */
  /**
   * Creates a deep copy of the `value`. Simple objects ({@link #function-isObject-static}, arrays and `Date` objects
   * are cloned. The enumerable properties of simple objects and the elements of arrays are cloned recursively.
   * @param {*} value The value to clone.
   * @param {Function} [handler] An optional function to call for values of types other than simple object, array or
   * `Date`. This function should return the clone of the `value` passed to it. It is only called for truthy values
   * whose `typeof` equals `'object'`.
   * @param {*} handler.value The value to clone.
   * @returns {*} The cloned value.
   * @method clone
   * @static
   */
  /**
   * Converts a list of names (either a space separated string or an array), into an object with those properties
   * assigned truthy values. The converse of {@link #function-getTruthyKeys-static}.
   * @param {String|String[]} source The list of names to convert to object form.
   * @method createTruthyKeys
   * @static
   */
  /**
   * Gathers the names of properties which have truthy values into an array.
   *
   * This is useful when gathering CSS class names for complex element production.
   * Instead of appending to an array or string which may already contain the
   * name, and instead of contending with space separation and concatenation
   * and conditional execution, just set the properties of an object:
   *
   *     cls = {
   *         [this.selectedCls] : this.isSelected(thing),
   *         [this.dirtyCls] : this.isDirty(thing)
   *     };
   *
   * If the `source` parameter is an array, it is returned. If it is a single string, it is wrapped in an array and
   * returned. Otherwise, the value must be an object (or null) it will be processed as described above.
   *
   * @param {Object|String|String[]} source Source of keys to gather into an array.
   * @returns {String[]} The keys which had a truthy value.
   * @method getTruthyKeys
   * @static
   */
  /**
   * Gathers the values of properties which are truthy into an array.
   * @param {Object} source Source of values to gather into an array.
   * @returns {String[]} The truthy values from the passed object.
   * @method getTruthyValues
   * @static
   */
  /**
   * Tests whether a passed object has any enumerable properties.
   * @param {Object} object
   * @returns {Boolean} `true` if the passed object has no enumerable properties.
   * @method isEmpty
   * @static
   */
  /**
   * Returns `true` if the `value` is a simple `Object`.
   * @param {Object} value
   * @returns {Boolean} `true` if the `value` is a simple `Object`.
   * @method isObject
   * @static
   */
  /**
   * Copies all enumerable properties from the supplied source objects to `dest`, recursing when the properties of
   * both the source and `dest` are objects.
   * ```
   *  const o = {
   *      a : 1,
   *      b : {
   *          c : 2
   *      }
   *  };
   *  const o2 = {
   *      b : {
   *          d : 3
   *      }
   *  }
   *
   *  console.log(merge(o, o2));
   *
   *  > { a : 1, b : { c : 2, d : 3 } }
   * ```
   * @param {Object} dest The destination object.
   * @param {...Object} sources The source objects.
   * @returns {Object} The `dest` object.
   * @method merge
   * @static
   */
  /**
   * Returns the specific type of the given `value`. Unlike the `typeof` operator, this function returns the text
   * from the `Object.prototype.toString` result allowing `Date`, `Array`, `RegExp`, and others to be differentiated.
   * ```
   *  console.log(typeOf(null));
   *  > null
   *
   *  console.log(typeOf({}));
   *  > object
   *
   *  console.log(typeOf([]));
   *  > array
   *
   *  console.log(typeOf(new Date()));
   *  > date
   *
   *  console.log(typeOf(NaN));
   *  > nan
   *
   *  console.log(typeOf(/a/));
   *  > regexp
   * ```
   * @param {*} value
   * @returns {String}
   * @method typeOf
   * @static
   */
  /**
   * Returns value for a given path in the object
   * @param {Object} object Object to check path on
   * @param {String} path Dot-separated path, e.g. 'object.childObject.someKey'
   * @returns {*} Value associated with passed key
   * @method getPath
   * @static
   */
  /**
   * Sets value for a given path in the object
   * @param {Object} object Target object
   * @param {String} path Dot-separated path, e.g. 'object.childObject.someKey'
   * @param {*} value Value for a given path
   * @returns {Object} Returns passed object
   * @method setPath
   * @static
   */
  /**
   * Calls a provided function `fn` for each key/value pair of a given `object`.
   * @param {Object} object The object to iterate
   * @param {Function} fn The function to call with each property and value of the given `object`.
   * @param {*} fn.value The value of the `object` property `name`.
   * @param {String} fn.name The name of the current property for the iteration.
   */
  static forEach(object, fn2) {
    let key;
    if (object) {
      if (Array.isArray(object)) {
        object.forEach(fn2);
      } else {
        for (key in object) {
          fn2(object[key], key);
        }
      }
    }
  }
  /**
   * Creates a new object where key is a property in array item (`ref` by default) or index in the array and value is array item.
   *
   * From:
   * ```
   * [
   *     {
   *          text : 'foo',
   *          ref : 'fooItem'
   *     },
   *     {
   *          text : 'bar'
   *     }
   * ]
   * ```
   *
   * To:
   * ```
   * {
   *     fooItem : {
   *         text : 'foo',
   *         ref  : 'fooItem'
   *     },
   *     1 : {
   *         text : 'bar'
   *     }
   * }
   * ```
   *
   * @param {Object[]} arrayOfItems Array to transform.
   * @param {String} [prop] Property to read the key from. `ref` by default.
   * @returns {Object} namedItems
   */
  static transformArrayToNamedObject(arrayOfItems, prop = "ref") {
    const namedItems = {};
    arrayOfItems.forEach((item, index) => {
      const key = item[prop] != null && item[prop].toString().length ? item[prop] : index;
      namedItems[key] = item;
    });
    return namedItems;
  }
  /**
   * Creates a new array from object values and saves key in a property (`ref` by default) of each item.
   *
   * From:
   * ```
   * {
   *     fooItem : {
   *         text : 'foo'
   *     },
   *     1 : {
   *         text : 'bar'
   *     },
   *     barItem : false // will be ignored
   * }
   * ```
   *
   * To:
   * ```
   * [
   *     {
   *          text : 'foo',
   *          ref : 'fooItem'
   *     },
   *     {
   *          text : 'bar',
   *          ref : 1
   *     }
   * ]
   * ```
   *
   * @param {Object} namedItems Object to transform.
   * @param {String} [prop] Property to save the key to. `ref` by default.
   * @returns {Object[]} arrayOfItems
   */
  static transformNamedObjectToArray(namedItems, prop = "ref") {
    return Object.keys(namedItems).filter((key) => namedItems[key]).map((key) => {
      const item = namedItems[key];
      item[prop] = key;
      return item;
    });
  }
  /**
   * Checks if two values are equal. Basically === but special handling of dates.
   * @param {*} a First value
   * @param {*} b Second value
   * @param {Boolean} [useIsDeeply=false] Pass `true` to match arrays & objects using a deep equality check.
   * @param {Boolean} [matchNullish=false] Pass `true` to have nullish (`undefined` and `null`) evaluated as equal.
   * @returns {*} true if values are equal, otherwise false
   */
  static isEqual(a, b, useIsDeeply = false, matchNullish = false) {
    if (!matchNullish && (a === null && b !== null || a === void 0 && b !== void 0 || b === null && a !== null || b === void 0 && a !== void 0)) {
      return false;
    }
    if (a == null || b == null) {
      return a == null && b == null;
    }
    if (a === b) {
      return true;
    }
    const typeA = typeof a, typeB = typeof b;
    if (typeA === typeB) {
      switch (typeA) {
        case "number":
        case "string":
        case "boolean":
          return a === b;
      }
      switch (true) {
        case (a.isModel && b.isModel):
          return a.equals(b);
        case (a.isBase && b.isBase):
          return a === b;
        case (a instanceof Date && b instanceof Date):
          return a.getTime() === b.getTime();
        case (Array.isArray(a) && Array.isArray(b)):
          return a.length === b.length ? a.every((v, idx) => OH.isEqual(v, b[idx], useIsDeeply)) : false;
        case (typeA === "object" && a.constructor.prototype === b.constructor.prototype):
          return useIsDeeply ? OH.isDeeplyEqual(a, b, useIsDeeply) : Helpers_default.String.safeJsonStringify(a, "circular") === Helpers_default.String.safeJsonStringify(b, "circular");
      }
    }
    return String(a) === String(b);
  }
  /**
   * Checks if two objects are deeply equal
   * @param {Object} a
   * @param {Object} b
   * @param {Object} [options] Additional comparison options
   * @param {Object} [options.ignore] Map of property names to ignore when comparing
   * @param {Function} [options.shouldEvaluate] Function used to evaluate if a property should be compared or not.
   * Return false to prevent comparison
   * @param {Function} [options.evaluate] Function used to evaluate equality. Return `true`/`false` as evaluation
   * result or anything else to let `isEqual` handle the comparison
   * @returns {Boolean}
   */
  static isDeeplyEqual(a, b, options = {}) {
    if (a === b) {
      return true;
    }
    if (!a || !b) {
      return false;
    }
    const aKeys = OH.keys(a, options.ignore), bKeys = OH.keys(b, options.ignore);
    if (aKeys.length !== bKeys.length) {
      return false;
    }
    for (let i = 0; i < aKeys.length; i++) {
      const aKey = aKeys[i], bKey = bKeys[i];
      if (aKey !== bKey) {
        return false;
      }
      const aVal = a[aKey], bVal = b[bKey];
      if (options.shouldEvaluate) {
        if (options.shouldEvaluate(
          aKey,
          {
            value: aVal,
            object: a
          },
          {
            value: bVal,
            object: b
          }
        ) === false) {
          continue;
        }
      }
      if (options.evaluate) {
        const result = options.evaluate(aKey, {
          value: aVal,
          object: a
        }, {
          value: bVal,
          object: b
        });
        if (result === false) {
          return false;
        }
        if (result === true) {
          continue;
        }
      }
      if (!OH.isEqual(aVal, bVal, options)) {
        return false;
      }
    }
    return true;
  }
  /**
   * Checks if value B is partially equal to value A.
   * @param {*} a First value
   * @param {*} b Second value
   * @returns {Boolean} true if values are partially equal, false otherwise
   */
  static isPartial(a, b) {
    a = String(a).toLowerCase();
    b = String(b).toLowerCase();
    return a.indexOf(b) !== -1;
  }
  /**
   * Checks if value a is smaller than value b.
   * @param {*} a First value
   * @param {*} b Second value
   * @returns {Boolean} true if a < b
   */
  static isLessThan(a, b) {
    if (a instanceof Date && b instanceof Date) {
      return Helpers_default.Date.isBefore(a, b);
    }
    return a < b;
  }
  /**
   * Checks if value a is bigger than value b.
   * @param {*} a First value
   * @param {*} b Second value
   * @returns {Boolean} true if a > b
   */
  static isMoreThan(a, b) {
    if (a instanceof Date && b instanceof Date) {
      return Helpers_default.Date.isAfter(a, b);
    }
    return a > b;
  }
  /**
   * If `value` is iterable (`for-of` loopable), this method returns the first element of the iterable sequence.
   * If `value` is async-iterable (i.e., uses a `for await of` loop), this method returns a `Promise` that will
   * resolve to the first element.
   * Otherwise, this method returns the first key/value pair (a 2-element array) using a `for-in` loop.
   * @param {*} value
   * @param {*} [defaultValue] The value to return if `value` is empty.
   * @returns {*}
   * @internal
   */
  static first(value2, defaultValue2) {
    let ret = defaultValue2, key;
    if (value2) {
      if (value2[Symbol.iterator]) {
        for (ret of value2) {
          break;
        }
      } else if (value2[Symbol.asyncIterator]) {
        ret = firstOfAsync(value2, defaultValue2);
      } else if (_ObjectHelper.isObject(value2)) {
        if (ret === void 0) {
          ret = EMPTY_ARRAY;
        }
        for (key in value2) {
          ret = [key, value2[key]];
          break;
        }
      }
    }
    return ret;
  }
  /**
   * Used by the Base class to make deep copies of defaultConfig blocks
   * @private
   */
  static fork(obj) {
    let ret, key, value2;
    if (obj && obj.constructor === Object) {
      ret = Object.create(obj);
      for (key in obj) {
        value2 = obj[key];
        if (value2) {
          if (value2.constructor === Object) {
            ret[key] = OH.fork(value2);
          } else if (value2 instanceof Array) {
            ret[key] = value2.slice();
          }
        }
      }
    } else {
      ret = obj;
    }
    return ret;
  }
  /**
   * Copies the named properties from the `source` parameter into the `dest` parameter.
   * @param {Object} dest The destination into which properties are copied.
   * @param {Object} source The source from which properties are copied.
   * @param {String[]} props The list of property names.
   * @returns {Object} The `dest` object.
   */
  static copyProperties(dest, source, props) {
    let prop, i;
    for (i = 0; i < props.length; i++) {
      prop = props[i];
      if (prop in source) {
        dest[prop] = source[prop];
      }
    }
    return dest;
  }
  /**
   * Copies the named properties from the `source` parameter into the `dest` parameter
   * unless the property already exists in the `dest`.
   * @param {Object} dest The destination into which properties are copied.
   * @param {Object} source The source from which properties are copied.
   * @param {String[]} props The list of property names.
   * @returns {Object} The `dest` object.
   */
  static copyPropertiesIf(dest, source, props) {
    if (source) {
      for (const prop of props) {
        if (!(prop in dest) && prop in source) {
          dest[prop] = source[prop];
        }
      }
    }
    return dest;
  }
  /**
   * Copies all properties from the `source` parameter into the `dest` parameter except those specified in `except`.
   * @param {Object} dest The destination into which properties are copied.
   * @param {Object} source The source from which properties are copied.
   * @param {Object} except Object whose keys specify the properties to exclude.
   * @returns {Object} The `dest` object.
   */
  static copyPropertiesExcept(dest, source, except) {
    if (source) {
      for (const prop in source) {
        if (!(except == null ? void 0 : except[prop])) {
          dest[prop] = source[prop];
        }
      }
    }
    return dest;
  }
  /**
   * Returns an array containing the keys and values of all enumerable properties from every prototype level for the
   * object. If `object` is `null`, this method returns an empty array.
   * @param {Object} object Object from which to retrieve entries.
   * @param {Object|Function} [ignore] Optional object of names to ignore or a function accepting the name and value
   * which returns `true` to ignore the item.
   * @returns {Array}
   * @internal
   */
  static entries(object, ignore) {
    const result = [], call = typeof ignore === "function";
    if (object) {
      for (const p in object) {
        if (call ? !ignore(p, object[p]) : !(ignore == null ? void 0 : ignore[p])) {
          result.push([p, object[p]]);
        }
      }
    }
    return result;
  }
  /**
   * Populates an `object` with the provided `entries`.
   * @param {Array} entries The key/value pairs (2-element arrays).
   * @param {Object} [object={}] The object onto which to add `entries`.
   * @returns {Object} The passed `object` (by default, a newly created object).
   * @internal
   */
  static fromEntries(entries, object) {
    object = object || {};
    if (entries) {
      for (let i = 0; i < entries.length; ++i) {
        object[entries[i][0]] = entries[i][1];
      }
    }
    return object;
  }
  /**
   * Returns an array containing all enumerable property names from every prototype level for the object. If `object`
   * is `null`, this method returns an empty array.
   * @param {Object} object Object from which to retrieve property names.
   * @param {Object|Function} [ignore] Optional object of names to ignore or a function accepting the name and value
   * which returns `true` to ignore the item.
   * @param {Function} [mapper] Optional function to call for each non-ignored item. If provided, the result of this
   * function is stored in the returned array. It is called with the array element as the first parameter, and the
   * index in the result array as the second argument (0 for the first, non-ignored element, 1 for the second and so
   * on).
   * @returns {String[]}
   */
  static keys(object, ignore, mapper) {
    const result = [], call = typeof ignore === "function";
    if (object) {
      let index = 0;
      for (const p in object) {
        if (call ? !ignore(p, object[p]) : !(ignore == null ? void 0 : ignore[p])) {
          result.push(mapper ? mapper(p, index) : p);
          ++index;
        }
      }
    }
    return result;
  }
  /**
   * Returns an array containing the values of all enumerable properties from every prototype level for the object.
   * If `object` is `null`, this method returns an empty array.
   * @param {Object} object Object from which to retrieve values.
   * @param {Object|Function} [ignore] Optional object of names to ignore or a function accepting the name and value
   * which returns `true` to ignore the item.
   * @param {Function} [mapper] Optional function to call for each non-ignored item. If provided, the result of this
   * function is stored in the returned array. It is called with the array element as the first parameter, and the
   * index in the result array as the second argument (0 for the first, non-ignored element, 1 for the second and so
   * on).
   * @returns {Array}
   * @internal
   */
  static values(object, ignore, mapper) {
    const result = [], call = typeof ignore === "function";
    if (object) {
      let index = 0;
      for (const p in object) {
        if (call ? !ignore(p, object[p]) : !(ignore == null ? void 0 : ignore[p])) {
          result.push(mapper ? mapper(object[p], index) : object[p]);
          ++index;
        }
      }
    }
    return result;
  }
  //region Path
  /**
   * Checks if a given path exists in an object
   * @param {Object} object Object to check path on
   * @param {String} path Dot-separated path, e.g. 'object.childObject.someKey'
   * @returns {Boolean} Returns `true` if path exists or `false` if it does not
   */
  static pathExists(object, path) {
    const properties = path.split(".");
    return properties.every((property) => {
      if (!object || !(property in object)) {
        return false;
      }
      object = object[property];
      return true;
    });
  }
  /**
   * Creates a simple single level key-value object from complex deep object.
   * @param {Object} object Object to extract path and values from
   * @returns {Object} Key-value object where key is a path to the corresponding value
   * @internal
   *
   * ```javascript
   * // converts deep object
   * {
   *     foo : {
   *         bar : {
   *             test : 1
   *         }
   *     }
   * }
   * // into a single level object
   * {
   *     'foo.bar.test' : 1
   * }
   * ```
   */
  static pathifyKeys(object, fieldDataSourceMap) {
    const result = {};
    for (const key in object) {
      if (hasOwn(object, key)) {
        const field = fieldDataSourceMap == null ? void 0 : fieldDataSourceMap[key];
        const usesPathKeys = (field == null ? void 0 : field.type) === "object" || (field == null ? void 0 : field.complexMapping) || !Boolean(fieldDataSourceMap);
        if (usesPathKeys && Array.isArray(object[key])) {
          result[key] = object[key].slice();
        } else if (usesPathKeys && object[key] instanceof Object) {
          const paths = this.pathifyKeys(object[key]);
          for (const path in paths) {
            result[`${key}.${path}`] = paths[path];
          }
        } else {
          result[key] = object[key];
        }
      }
    }
    return result;
  }
  /**
   * Removes value for a given path in the object. Doesn't cleanup empty objects.
   * @param {Object} object
   * @param {String} path Dot-separated path, e.g. `obj.child.someKey`
   * @internal
   */
  static deletePath(object, path) {
    path.split(".").reduce((result, key, index, array) => {
      if (result == null) {
        return null;
      }
      if (hasOwn(result, key)) {
        if (index === array.length - 1) {
          delete result[key];
        } else {
          return result[key];
        }
      }
    }, object);
  }
  //endregion
  static coerce(from, to) {
    const fromType = Objects.typeOf(from), toType = Objects.typeOf(to), isString = typeof from === "string";
    if (fromType !== toType) {
      switch (toType) {
        case "string":
          return String(from);
        case "number":
          return Number(from);
        case "boolean":
          return isString && (!from || from === "false" || from === "0") ? false : Boolean(from);
        case "null":
          return isString && (!from || from === "null") ? null : false;
        case "undefined":
          return isString && (!from || from === "undefined") ? void 0 : false;
        case "date":
          return isString && isNaN(from) ? Helpers_default.Date.parse(from) : Date(Number(from));
      }
    }
    return from;
  }
  static wrapProperty(object, propertyName, newGetter, newSetter, deep = true) {
    const newProperty = {};
    let proto4 = Object.getPrototypeOf(object), existingProperty = Object.getOwnPropertyDescriptor(proto4, propertyName);
    while (!existingProperty && proto4 && deep) {
      proto4 = Object.getPrototypeOf(proto4);
      if (proto4) {
        existingProperty = Object.getOwnPropertyDescriptor(proto4, propertyName);
      }
    }
    if (existingProperty) {
      if (existingProperty.set) {
        newProperty.set = (v) => {
          existingProperty.set.call(object, v);
          newSetter && newSetter.call(object, existingProperty.get.call(object));
        };
      } else {
        newProperty.set = newSetter;
      }
      if (existingProperty.get) {
        newProperty.get = () => {
          let result = existingProperty.get.call(object);
          if (newGetter) {
            result = newGetter.call(object, result);
          }
          return result;
        };
      } else {
        newProperty.get = newGetter;
      }
    } else {
      newProperty.set = (v) => {
        object[`_${propertyName}`] = v;
        newSetter && newSetter.call(object, v);
      };
      newProperty.get = () => {
        let result = object[`_${propertyName}`];
        if (newGetter) {
          result = newGetter.call(object, result);
        }
        return result;
      };
    }
    Object.defineProperty(object, propertyName, newProperty);
  }
  /**
   * Intercepts access to a `property` of a given `object`.
   *
   * ```javascript
   *      ObjectHelper.hookProperty(object, 'prop', class {
   *          get value() {
   *              return super.value;
   *          }
   *          set value(v) {
   *              super.value = v;
   *          }
   *      });
   * ```
   * The use of `super` allows the hook's getter and setter to invoke the object's existing get/set.
   *
   * @param {Object} object
   * @param {String} property
   * @param {Function} hook A `class` defining a `value` property getter and/or setter.
   * @returns {Function} A function that removes the hook when called.
   * @internal
   */
  static hookProperty(object, property, hook) {
    const desc = _ObjectHelper.getPropertyDescriptor(hook.prototype, "value"), existingDesc = _ObjectHelper.getPropertyDescriptor(object, property), fieldName = `_${property}`, base = class {
      get value() {
        return existingDesc ? existingDesc.get.call(this) : this[fieldName];
      }
      set value(v) {
        if (existingDesc) {
          existingDesc.set.call(this, v);
        } else {
          this[fieldName] = v;
        }
      }
    }, baseDesc = _ObjectHelper.getPropertyDescriptor(base.prototype, "value");
    Object.setPrototypeOf(hook.prototype, base.prototype);
    Object.defineProperty(object, property, {
      configurable: true,
      get: desc.get || baseDesc.get,
      set: desc.set || baseDesc.set
    });
    return () => delete object[property];
  }
  /**
   * Changes the passed object and removes all null and undefined properties from it
   * @param {Object} object Target object
   * @param {Boolean} [keepNull] Pass true to only remove undefined properties
   * @returns {Object} Passed object
   */
  static cleanupProperties(object, keepNull = false) {
    Object.entries(object).forEach(([key, value2]) => {
      if (keepNull) {
        value2 === void 0 && delete object[key];
      } else {
        value2 == null && delete object[key];
      }
    });
    return object;
  }
  /**
   * Changes the passed object and removes all properties from it.
   * Used while mutating when need to keep reference to the object but replace its properties.
   * @param {Object} object Target object
   * @returns {Object} Passed object
   */
  static removeAllProperties(obj) {
    Object.keys(obj).forEach((key) => delete obj[key]);
    return obj;
  }
  //region Assert type
  /**
   * Checks that the supplied value is of the specified type.Throws if it is not
   * @param {Object} value Value to check type of
   * @param {String} type Expected type
   * @param {String} name Name of the value, used in error message
   * @param {Boolean} [allowNull] Accept `null` without throwing
   */
  static assertType(value2, type, name) {
    const valueType = Objects.typeOf(value2);
    if (value2 != null && valueType !== type) {
      throw new Error(`Incorrect type "${valueType}" for ${name}, expected "${type}"`);
    }
  }
  /**
   * Checks that the supplied value is a plain object. Throws if it is not
   * @param {Object} value Value to check type of
   * @param {String} name Name of the value, used in error message
   */
  static assertObject(value2, name) {
    OH.assertType(value2, "object", name);
  }
  /**
   * Checks that the supplied value is an instance of a Bryntum class. Throws if it is not
   * @param {Object} value Value to check type of
   * @param {String} name Name of the value, used in error message
   */
  static assertInstance(value2, name) {
    OH.assertType(value2, "instance", name);
  }
  /**
   * Checks that the supplied value is a Bryntum class. Throws if it is not
   * @param {Object} value Value to check type of
   * @param {String} name Name of the value, used in error message
   */
  static assertClass(value2, name) {
    OH.assertType(value2, "class", name);
  }
  /**
   * Checks that the supplied value is a function. Throws if it is not
   * @param {Object} value Value to check type of
   * @param {String} name Name of the value, used in error message
   */
  static assertFunction(value2, name) {
    if (typeof value2 !== "function" || value2.isBase || value2.$$name) {
      throw new Error(`Incorrect type for ${name}, got "${value2}" (expected a function)`);
    }
  }
  /**
   * Checks that the supplied value is a number. Throws if it is not
   * @param {Object} value Value to check type of
   * @param {String} name Name of the value, used in error message
   */
  static assertNumber(value2, name, positive) {
    const asNumber = Number(value2);
    if (typeof value2 !== "number" || isNaN(asNumber) || positive && value2 < 0) {
      throw new Error(`Incorrect type for ${name}, got "${value2}" (expected a ${positive ? "positive " : ""}Number)`);
    }
  }
  /**
   * Checks that the supplied value is a boolean. Throws if it is not
   * @param {Object} value Value to check type of
   * @param {String} name Name of the value, used in error message
   */
  static assertBoolean(value2, name) {
    OH.assertType(value2, "boolean", name);
  }
  /**
   * Checks that the supplied value is a string. Throws if it is not
   * @param {Object} value Value to check type of
   * @param {String} name Name of the value, used in error message
   */
  static assertString(value2, name) {
    OH.assertType(value2, "string", name);
  }
  /**
   * Checks that the supplied value is an array. Throws if it is not
   * @param {Object} value Value to check type of
   * @param {String} name Name of the value, used in error message
   */
  static assertArray(value2, name) {
    OH.assertType(value2, "array", name);
  }
  //endregion
  /**
   * Number.toFixed(), with polyfill for browsers that needs it
   * @param {Number} number
   * @param {Number} digits
   * @returns {String} A fixed point string representation of the passed number.
   */
  static toFixed(number, digits) {
    if (toFixedFix) {
      return toFixedFix(number, digits);
    }
    return number.toFixed(digits);
  }
  /**
   * Round the passed number to closest passed step value.
   * @param {Number} number The number to round.
   * @param {Number} [step] The step value to round to.
   * @returns {Number} The number rounded to the closest step.
   */
  static roundTo(number, step = 1) {
    return Math.round(number / step) * step;
  }
  /**
   * Round the passed number to the passed number of decimals.
   * @param {Number} number The number to round.
   * @param {Number} digits The number of decimal places to round to.
   * @returns {Number} The number rounded to the passed number of decimal places.
   */
  static round(number, digits) {
    if (digits == null) {
      return number;
    }
    const factor = 10 ** digits;
    return Math.round(number * factor) / factor;
  }
  /**
   * Returns a non-null entry from a Map for a given key path. This enables a specified defaultValue to be added "just
   * in time" which is returned if the key is not already present.
   * @param {Map} map The Map to find the key in (and potentially add to).
   * @param {String|Number|String[]|Number[]} path Dot-separated path, e.g. 'firstChild.childObject.someKey',
   * or the key path as an array, e.g. ['firstChild', 'childObject', 'someKey'].
   * @param {Object} [defaultValue] Optionally the value to insert if the key is not found.
   */
  static getMapPath(map2, path, defaultValue2) {
    const keyPath = Array.isArray(path) ? path : typeof path === "string" ? path.split(".") : [path], simpleKey = keyPath.length === 1, topKey = keyPath[0], topValue = map2.has(topKey) ? map2.get(topKey) : map2.set(topKey, simpleKey ? defaultValue2 : {}).get(topKey);
    if (simpleKey) {
      return topValue;
    }
    return OH.getPathDefault(topValue, keyPath.slice(1), defaultValue2);
  }
};
__publicField(_ObjectHelper, "$name", "ObjectHelper");
var ObjectHelper = _ObjectHelper;
Helpers_default.register(ObjectHelper);
var OH = ObjectHelper;
ObjectHelper._$name = "ObjectHelper";

// ../Core/lib/Core/helper/VersionHelper.js
var _VersionHelper = class _VersionHelper {
  /**
   * Set version for specified product
   * @private
   * @param {String} product
   * @param {String} version
   */
  static setVersion(product, version) {
    product = product.toLowerCase();
    VH[product] = {
      version,
      isNewerThan(otherVersion) {
        return _VersionHelper.semanticCompareVersion(otherVersion, version, "<");
      },
      isOlderThan(otherVersion) {
        return _VersionHelper.semanticCompareVersion(otherVersion, version, ">");
      }
    };
    let bundleFor = "";
    if (typeof productName !== "undefined") {
      bundleFor = `${productName}.`;
    }
    const globalKey = `${bundleFor}${product}${version.replace(/\./g, "-")}`;
    if (BrowserHelper.isBrowserEnv && !globalThis.bryntum.silenceBundleException) {
      if (globalThis.bryntum[globalKey] === true) {
        if (this.isTestEnv) {
          globalThis.BUNDLE_EXCEPTION = true;
        } else {
          let errorProduct = bundleFor || product;
          if (errorProduct === "core") {
            errorProduct = "grid";
          }
          let capitalized = StringHelper.capitalize(errorProduct);
          if (errorProduct === "schedulerpro") {
            capitalized = "SchedulerPro";
          }
          throw new Error(
            `The Bryntum ${capitalized} bundle was loaded multiple times by the application.

Common reasons you are getting this error includes:

* Imports point to different types of the bundle (e.g. *.module.js and *.umd.js)
* Imports point to both sources and bundle
* Imports do not use the shortest relative path, JS treats them as different files
* Cache busters differ between imports, JS treats ${errorProduct}.module.js?1 and ${errorProduct}.module.js?2 as different files
* Imports missing file type, verify they all end in .js

See https://bryntum.com/products/${errorProduct}/docs/guide/${capitalized}/gettingstarted/es6bundle#troubleshooting for more information

`
          );
        }
      } else {
        globalThis.bryntum[globalKey] = true;
      }
    }
  }
  /**
   * Get (previously set) version for specified product
   * @private
   * @param {String} product
   */
  static getVersion(product) {
    product = product.toLowerCase();
    if (!VH[product]) {
      throw new Error("No version specified! Please check that you import VersionHelper correctly into the class from where you call `deprecate` function.");
    }
    return VH[product].version;
  }
  /**
   * Checks the version1 against the passed version2 using the comparison operator.
   * Supports `rc`, `beta`, `alpha` release states. Eg. `1.2.3-alpha-1`.
   * State which is not listed above means some version below `alpha`.
   * @param {String} version1 The version to test against
   * @param {String} version2 The version to test against
   * @param {String} [comparison] The comparison operator, `<=`, `<`, `=`, `>` or `>=`.
   * @returns {Boolean} `true` if the test passes.
   * @internal
   */
  static semanticCompareVersion(version1, version2, comparison = "=") {
    version1 = version1 || "";
    version2 = version2 || "";
    const version1Arr = version1.split(/[-.]/), version2Arr = version2.split(/[-.]/), isLower = comparison.includes("<"), normalizeArr = (arr, maxLength) => {
      const states = ["rc", "beta", "alpha"], result = arr.map((v) => {
        if (states.includes(v)) {
          return -states.indexOf(v) - 2;
        }
        const res = Number.parseInt(v);
        return Number.isNaN(res) ? -states.length : res;
      });
      while (result.length < maxLength) {
        result.push(-1);
      }
      return result;
    }, compareArr = () => {
      const maxLength = Math.max(version1Arr.length, version2Arr.length), arr1 = normalizeArr(version1Arr, maxLength), arr2 = normalizeArr(version2Arr, maxLength);
      for (let i = 0; i < maxLength; i++) {
        if (arr1[i] !== arr2[i]) {
          return isLower ? arr1[i] < arr2[i] : arr1[i] > arr2[i];
        }
      }
      return true;
    };
    switch (comparison) {
      case "=":
        return version1 === version2;
      case "<=":
      case ">=":
        return version1 === version2 || compareArr();
      case "<":
      case ">":
        return version1 !== version2 && compareArr();
    }
    return false;
  }
  /**
   * Checks the passed product against the passed version using the passed test.
   * @param {String} product The name of the product to test the version of
   * @param {String} version The version to test against
   * @param {String} operator The test operator, `<=`, `<`, `=`, `>` or `>=`.
   * @returns {Boolean} `true` if the test passes.
   * @internal
   */
  static checkVersion(product, version, operator) {
    return _VersionHelper.semanticCompareVersion(VH.getVersion(product), version, operator);
  }
  /**
   * Based on a comparison of current product version and the passed version this method either outputs a console.warn
   * or throws an error.
   * @param {String} product The name of the product
   * @param {String} invalidAsOfVersion The version where the offending code is invalid (when any compatibility layer
   * is actually removed).
   * @param {String} message Required! A helpful warning message to show to the developer using a deprecated API.
   * @internal
   */
  static deprecate(product, invalidAsOfVersion, message) {
    const justWarn = VH.checkVersion(product, invalidAsOfVersion, "<");
    if (justWarn) {
      console.warn(`Deprecation warning: You are using a deprecated API which will change in v${invalidAsOfVersion}. ${message}`);
    } else {
      throw new Error(`Deprecated API use. ${message}`);
    }
  }
  /**
   * Returns truthy value if environment is in testing mode
   * @returns {Boolean}
   * @internal
   **/
  static get isTestEnv() {
    var _a5, _b2, _c2;
    const isTestEnv = Boolean((_a5 = globalThis.bryntum) == null ? void 0 : _a5.isTestEnv);
    try {
      return isTestEnv || Boolean((_c2 = (_b2 = globalThis.parent) == null ? void 0 : _b2.bryntum) == null ? void 0 : _c2.isTestEnv);
    } catch (e) {
      return isTestEnv;
    }
  }
  static get isDebug() {
    let result = false;
    return result;
  }
};
__publicField(_VersionHelper, "$name", "VersionHelper");
var VersionHelper = _VersionHelper;
var VH = VersionHelper;
if (BrowserHelper.isBrowserEnv) {
  if (VH.isTestEnv) {
    BrowserHelper._isHoverableDevice = true;
  }
  globalThis.bryntum = Object.assign(globalThis.bryntum || {}, {
    getVersion: VH.getVersion.bind(VH),
    checkVersion: VH.checkVersion.bind(VH),
    deprecate: VH.deprecate.bind(VH),
    license: "8931338c-dd36-11ef-ab90-0aa8b363f5dd"
  });
}
Helpers_default.register(VersionHelper);
VersionHelper._$name = "VersionHelper";

// ../Core/lib/Core/helper/FunctionHelper.js
var commaSepRe = /,\s*/;
var decompiledSym = Symbol("decompiled");
var fnRe1 = /^\s*(async\s+)?([a-z_]\w*)\s*=>([\s\S]+)$/i;
var fnRe2 = /^\s*(async\s*)?\s*\(((?:[a-z_]\w*(?:, [a-z_]\w*)*)?)\)\s+=>([\s\S]+)$/i;
var fnRe3 = /^(\s*async)?(?:\s*function)?(?:\s*([a-z_]\w*))?\s*\(((?:[a-z_]\w*(?:, [a-z_]\w*)*)?)\)([\s\S]+)$/i;
var { hasOwnProperty: hasOwnProperty3 } = Object.prototype;
var _FunctionHelper = class _FunctionHelper {
  /**
   * Inserts a function after the specified `method` is called on an `object`. To remove this hook, invoke the
   * function returned by this method.
   * ```
   *  class A {
   *      method() {
   *          console.log('method');
   *      }
   *  }
   *
   *  let instance = new A();
   *
   *  let detach = FunctionHelper.after(instance, 'method', () => { console.log('after') });
   *
   *  instance.method();
   *  > method
   *  > after
   *
   *  detach();
   *  instance.method();
   *  > method
   * ```
   * The value returned by the original method is passed as the first argument to `fn` followed by all the arguments
   * passed by the caller.
   *
   * If `fn` returns a value (not `undefined`), that value is returned from the method call instead of the value
   * returned by the original method.
   * ```
   *  class A {
   *      method(x) {
   *          console.log('method', x);
   *          return x * 2
   *      }
   *  }
   *
   *  let instance = new A();
   *
   *  let detach = FunctionHelper.after(instance, 'method', (ret, x) => {
   *      console.log('after', ret, x);
   *      return x / 2;
   *  });
   *
   *  console.log(instance.method(50));
   *  > method 50
   *  > after 100 50
   *  > 25
   *
   *  detach();
   *  console.log(instance.method(50));
   *  > method 50
   *  > 100
   * ```
   *
   * @param {Object} object The object to hook.
   * @param {String} method The name of the method on `object` to hook.
   * @param {Function|String} fn The function or method name (on `thisObj`) to call after `method`.
   * @param {Object} [thisObj] The `this` pointer value for calling `fn`.
   * @param {Object} [options] Additional options
   * @param {Boolean} [options.return=true] Specify `false` to not include the return value of the hooked method as
   * the first argument to `fn`.
   * @returns {Function} The function to call to remove the hook.
   */
  static after(object, method, fn2, thisObj, options) {
    const named = typeof fn2 === "string", withReturn = (options == null ? void 0 : options.return) !== false, hook = (...args) => {
      const origResult = hook.$nextHook.call(object, ...args), hookResult = (thisObj == null ? void 0 : thisObj.isDestroyed) ? void 0 : withReturn ? named ? thisObj[fn2](origResult, ...args) : fn2.call(thisObj, origResult, ...args) : named ? thisObj[fn2](...args) : fn2.call(thisObj, ...args);
      return hookResult === void 0 ? origResult : hookResult;
    };
    return _FunctionHelper.hookMethod(object, method, hook);
  }
  /**
   * Inserts a function before the specified `method` is called on an `object`. To remove this hook, invoke the
   * function returned by this method.
   * ```
   *  class A {
   *      method() {
   *          console.log('method');
   *      }
   *  }
   *
   *  let instance = new A();
   *
   *  let detach = FunctionHelper.before(instance, 'method', () => { console.log('before') });
   *
   *  instance.method();
   *  > before
   *  > method
   *
   *  detach();
   *  instance.method();
   *  > method
   * ```
   * If `fn` returns `false`, the original method is not invoked and `false` is returned to the caller.
   * ```
   *  class A {
   *      method(x) {
   *          console.log('method', x);
   *          return x * 2;
   *      }
   *  }
   *
   *  let instance = new A();
   *
   *  let detach = FunctionHelper.before(instance, 'method', x => {
   *      console.log('before', x);
   *      return false;
   *  });
   *
   *  console.log(instance.method(50));
   *  > before 50
   *  > false
   *
   *  detach();
   *  console.log(instance.method(50));
   *  > method 50
   *  > 100
   * ```
   *
   * @param {Object} object The object to hook.
   * @param {String} method The name of the method on `object` to hook.
   * @param {Function|String} fn The function or method name (on `thisObj`) to call before `method`.
   * @param {Object} [thisObj] The `this` pointer value for calling `fn`.
   * @returns {Function} The function to call to remove the hook.
   */
  static before(object, method, fn2, thisObj) {
    const named = typeof fn2 === "string", hook = (...args) => {
      const ret = (thisObj == null ? void 0 : thisObj.isDestroyed) ? 0 : named ? thisObj[fn2](...args) : fn2.call(thisObj, ...args);
      return ret === false ? ret : hook.$nextHook.call(object, ...args);
    };
    return _FunctionHelper.hookMethod(object, method, hook);
  }
  static curry(func) {
    return function curried(...args) {
      if (args.length >= func.length) {
        return func.apply(this, args);
      } else {
        return function(...args2) {
          return curried.apply(this, args.concat(args2));
        };
      }
    };
  }
  static bindAll(obj) {
    for (const key in obj) {
      if (typeof obj[key] === "function") {
        obj[key] = obj[key].bind(obj);
      }
    }
  }
  /**
   * Returns a function which calls the passed `interceptor` function first, and the passed `original` after
   * as long as the `interceptor` does not return `false`.
   * @param {Function} original The function to call second.
   * @param {Function} interceptor The function to call first.
   * @param {Object} [thisObj] The `this` reference when the functions are called.
   * @returns {Function} A function which yields the return value from the `original` function **if it was called**, else `false`.
   */
  static createInterceptor(original, interceptor, thisObj) {
    return function(...args) {
      const theThis = thisObj || this;
      if (interceptor.call(theThis, ...args) !== false) {
        return original.call(theThis, ...args);
      }
      return false;
    };
  }
  /**
   * Returns a function which calls the passed `sequence` function after calling
   * the passed `original`.
   * @param {Function} original The function to call first.
   * @param {Function} sequence The function to call second.
   * @param {Object} [thisObj] Overrides the `this` reference when the original function is called.
   * @param {Object} [sequenceThisObj] The `this` reference when the sequence function is called (Defaults to `thisObj`).
   * @returns {Function} A function which yields the value returned from the sequence if it returned a value, else the return
   * value from the original function.
   */
  static createSequence(original, sequence, thisObj, sequenceThisObj) {
    return function(...args) {
      const origResult = original.call(thisObj = thisObj || this, ...args), sequenceResult = sequence.call(sequenceThisObj || thisObj, ...args);
      return sequenceResult === void 0 ? origResult : sequenceResult;
    };
  }
  /**
   * Create a "debounced" function which will call on the "leading edge" of a timer period.
   * When first invoked will call immediately, but invocations after that inside its buffer
   * period will be rejected, and *one* invocation will be made after the buffer period has expired.
   *
   * This is useful for responding immediately to a first mousemove, but from then on, only
   * calling the action function on a regular timer while the mouse continues to move.
   *
   * @param {Function} fn The function to call.
   * @param {Number} buffer The milliseconds to wait after each execution before another execution takes place.
   * @param {Object} [thisObj] `this` reference for the function.
   * @param {Array} [extraArgs] The argument list to append to those passed to the function.
   * @param {Function} [alt] A function to call when the invocation is rejected due to buffer time not having expired.
   * @returns {Function} A function which calls the passed `fn` only if at least the passed `buffer`
   * milliseconds has elapsed since its last invocation.
   */
  static createThrottled(fn2, buffer, thisObj, extraArgs, alt2) {
    let lastCallTime = -Number.MAX_VALUE, callArgs, timerId;
    const invoke = () => {
      timerId = 0;
      lastCallTime = performance.now();
      callArgs.push.apply(callArgs, extraArgs);
      fn2.apply(thisObj, callArgs);
    }, result = function(...args) {
      const elapsed = performance.now() - lastCallTime;
      callArgs = args;
      if (elapsed >= buffer) {
        clearTimeout(timerId);
        invoke();
      } else {
        if (!timerId) {
          timerId = setTimeout(invoke, buffer - elapsed);
        }
        if (alt2) {
          callArgs.push.apply(callArgs, extraArgs);
          alt2.apply(thisObj, callArgs);
        }
      }
    };
    result.cancel = () => clearTimeout(timerId);
    return result;
  }
  /**
   * Create a "debounced" function which will call on the "trailing edge" of a timer period.
   * When first invoked will wait until the buffer period has expired to call the function, and
   * more calls within that time will restart the timer.
   *
   * This is useful for responding to keystrokes, but deferring action until the user pauses typing.
   *
   * @param {Function} fn The function to call.
   * @param {Number} buffer The milliseconds to wait after each execution before another execution takes place.
   * @param {Object} [thisObj] `this` reference for the function.
   * @param {Array} [args] The argument list to append to those passed to the function.
   * @returns {Function} A function which calls the passed `fn` when at least the passed `buffer`
   * milliseconds has elapsed since its last invocation.
   */
  static createBuffered(fn2, buffer, thisObj, args) {
    let callArgs, timerId;
    const invoke = () => {
      timerId = 0;
      result.isPending = false;
      callArgs.push.apply(callArgs, args);
      fn2.apply(thisObj, callArgs);
    }, result = function(...args2) {
      callArgs = args2;
      if (timerId) {
        clearTimeout(timerId);
      }
      result.isPending = true;
      timerId = setTimeout(invoke, buffer);
    };
    result.cancel = () => {
      result.isPending = false;
      clearTimeout(timerId);
    };
    return result;
  }
  static decompile(fn2) {
    if (!(decompiledSym in fn2)) {
      const code = fn2.toString();
      let m = fnRe1.exec(code), args, body, name, decompiled, t;
      if (m) {
        args = [m[2]];
        body = m[3];
      } else if (m = fnRe2.exec(code)) {
        t = m[2].trim();
        args = t ? t.split(commaSepRe) : [];
        body = m[3];
      } else if (m = fnRe3.exec(code)) {
        name = m[2];
        t = m[3].trim();
        args = t ? t.split(commaSepRe) : [];
        body = m[4];
      }
      body = body == null ? void 0 : body.trim();
      fn2[decompiledSym] = decompiled = m && {
        args,
        async: Boolean(m[1]),
        body: (body == null ? void 0 : body.startsWith("{")) ? body.substring(1, body.length - 1).trim() : body
      };
      if (name) {
        decompiled.name = name;
      }
    }
    return fn2[decompiledSym];
  }
  static hookMethod(object, method, hook) {
    hook.$nextHook = object[method];
    object[method] = hook;
    return () => {
      var _a5;
      if (hasOwnProperty3.call(object, method)) {
        let f = object[method], next;
        if (f === hook) {
          if (((_a5 = Object.getPrototypeOf(object)) == null ? void 0 : _a5[method]) === hook.$nextHook) {
            delete object[method];
          } else {
            object[method] = hook.$nextHook;
          }
        } else {
          for (; next = f == null ? void 0 : f.$nextHook; f = next) {
            if (next === hook) {
              f.$nextHook = hook.$nextHook;
              break;
            }
          }
        }
      }
    };
  }
  /**
   * Protects the specified `method` on a given `object` such that calling it will not throw exceptions.
   * @param {Object} object The object whose method is to be protected.
   * @param {String} method The name of the method to protect.
   * @param {Function} [handler] An optional function to call for any thrown exceptions.
   * @internal
   */
  static noThrow(object, method, handler) {
    const fn2 = object[method];
    object[method] = (...args) => {
      try {
        return fn2.apply(object, args);
      } catch (e) {
        return handler == null ? void 0 : handler(e);
      }
    };
  }
  static returnTrue() {
    return true;
  }
  /**
   * Calls the passed function on every animation frame while animating the `progress` value from 0 to 1 over the specified duration.
   *
   * The function is called with the current `progress` value as the first argument. That value changes from
   * `0` to `1` over the duration of the animation at a rate determined by the `easing` function.
   *
   * The called function may change the value of any property or perform some other action based on the
   * current `progress` value.
   *
   * The function should return `false` to indicate that the animation should be stopped immediately.
   *
   * ```javascript
   * // Increase hourHeight by 20 pixels in a visually appealing way
   * const
   *     { activeView } = this,
   *     { hourHeight } = activeView;
   *
   * FunctionHelper.animate(
   *     1000,
   *     progress => activeView.hourHeight = hourHeight * progress * 20,
   *     null,
   *     'bounce'
   * );
   * ```
   * @param {Number} duration The duration of the animation in milliseconds.
   * @param {Function} fn The function to call for each animation frame.
   * @param {Number} fn.progress The current progress value from `0` to `1`.
   * @param {Object} thisObj The `this` reference for the function.
   * @param {'linear'|'easeInBack'|'easeOutBack'|'easeInOutBack'|'easeOutBounce'|'easeInOutBounce'|'easeInCirc'|'easeOutCirc'|'easeInOutCirc'|'easeInCubic'|'easeOutCubic'|'easeInOutCubic'|'easeInElastic'|'easeOutElastic'|'easeInOutElastic'|'easeInExpo'|'easeOutExpo'|'easeInOutExpo'|'easeInQuad'|'easeOutQuad'|'easeInOutQuad'|'easeInQuart'|'easeOutQuart'|'easeInOutQuart'|'easeInQuint'|'easeOutQuint'|'easeInOutQuint'|'easeInSine'|'easeOutSine'|'easeInOutSine'} [easing='linear'] The easing function to use.
   * @returns {Promise} A promise which resolves when the animation completes.
   */
  static animate(duration, fn2, thisObj, easing = "linear") {
    let cancel = false;
    const result = new Promise((resolve) => {
      const start = performance.now(), iterate = () => {
        const progress = Math.min((performance.now() - start) / duration, 1), delayable = (thisObj == null ? void 0 : thisObj.setTimeout) ? thisObj : globalThis;
        if (!cancel) {
          if (fn2.call(thisObj, this.easingFunctions[easing](progress)) === false) {
            resolve();
          }
        }
        if (cancel || progress === 1) {
          delayable.requestAnimationFrame(() => resolve());
        } else {
          delayable.requestAnimationFrame(iterate);
        }
      };
      iterate();
    });
    result.cancel = () => {
      cancel = true;
      result.cancelled = true;
      return false;
    };
    return result;
  }
};
__publicField(_FunctionHelper, "$name", "FunctionHelper");
var FunctionHelper = _FunctionHelper;
Helpers_default.register(FunctionHelper);
var half = 0.5;
var e1 = 1.70158;
var e2 = 7.5625;
var e3 = 1.525;
var e4 = 2 / 2.75;
var e5 = 2.25 / 2.75;
var e6 = 1 / 2.75;
var e7 = 1.5 / 2.75;
var e8 = 2.5 / 2.75;
var e9 = 2.625 / 2.75;
var e10 = 0.75;
var e11 = 0.9375;
var e12 = 0.984375;
var s1 = 1.70158;
var s2 = 1.70158;
FunctionHelper.easingFunctions = {
  linear: (t) => t,
  easeInQuad: (t) => Math.pow(t, 2),
  easeOutQuad: (t) => -(Math.pow(t - 1, 2) - 1),
  easeInOutQuad: (t) => (t /= half) < 1 ? half * Math.pow(t, 2) : -half * ((t -= 2) * t - 2),
  easeInCubic: (t) => Math.pow(t, 3),
  easeOutCubic: (t) => Math.pow(t - 1, 3) + 1,
  easeInOutCubic: (t) => (t /= half) < 1 ? half * Math.pow(t, 3) : half * (Math.pow(t - 2, 3) + 2),
  easeInQuart: (t) => Math.pow(t, 4),
  easeOutQuart: (t) => -(Math.pow(t - 1, 4) - 1),
  easeInOutQuart: (t) => (t /= half) < 1 ? half * Math.pow(t, 4) : -half * ((t -= 2) * Math.pow(t, 3) - 2),
  easeInQuint: (t) => Math.pow(t, 5),
  easeOutQuint: (t) => Math.pow(t - 1, 5) + 1,
  easeInOutQuint: (t) => (t /= half) < 1 ? half * Math.pow(t, 5) : half * (Math.pow(t - 2, 5) + 2),
  easeInSine: (t) => -Math.cos(t * (Math.PI / 2)) + 1,
  easeOutSine: (t) => Math.sin(t * (Math.PI / 2)),
  easeInOutSine: (t) => -half * (Math.cos(Math.PI * t) - 1),
  easeInExpo: (t) => t === 0 ? 0 : Math.pow(2, 10 * (t - 1)),
  easeOutExpo: (t) => t === 1 ? 1 : -Math.pow(2, -10 * t) + 1,
  easeInOutExpo: (t) => t === 0 ? 0 : t === 1 ? 1 : (t /= half) < 1 ? half * Math.pow(2, 10 * (t - 1)) : half * (-Math.pow(2, -10 * --t) + 2),
  easeInCirc: (t) => -(Math.sqrt(1 - t * t) - 1),
  easeOutCirc: (t) => Math.sqrt(1 - Math.pow(t - 1, 2)),
  easeInOutCirc: (t) => (t /= half) < 1 ? -half * (Math.sqrt(1 - t * t) - 1) : half * (Math.sqrt(1 - (t -= 2) * t) + 1),
  easeOutBounce: (t) => t < e6 ? e2 * t * t : t < e4 ? e2 * (t -= e7) * t + e10 : t < e8 ? e2 * (t -= e5) * t + e11 : e2 * (t -= e9) * t + e12,
  easeInBack: (t) => t * t * ((e1 + 1) * t - e1),
  easeOutBack: (t) => (t = t - 1) * t * ((e1 + 1) * t + e1) + 1,
  easeInOutBack: (t) => {
    let v1 = s1;
    return (t /= half) < 1 ? half * (t * t * (((v1 *= e3) + 1) * t - v1)) : half * ((t -= 2) * t * (((v1 *= e3) + 1) * t + v1) + 2);
  },
  elastic: (t) => -1 * Math.pow(4, -8 * t) * Math.sin((t * 6 - 1) * (2 * Math.PI) / 2) + 1,
  swingFromTo: (t) => {
    let v2 = s2;
    return (t /= half) < 1 ? half * (t * t * (((v2 *= e3) + 1) * t - v2)) : half * ((t -= 2) * t * (((v2 *= e3) + 1) * t + v2) + 2);
  },
  swingFrom: (t) => t * t * ((e1 + 1) * t - e1),
  swingTo: (t) => (t -= 1) * t * ((e1 + 1) * t + e1) + 1,
  bounce: (t) => t < e6 ? e2 * t * t : t < e4 ? e2 * (t -= e7) * t + e10 : t < e8 ? e2 * (t -= e5) * t + e11 : e2 * (t -= e9) * t + e12,
  bouncePast: (t) => t < e6 ? e2 * t * t : t < e4 ? 2 - (e2 * (t -= e7) * t + e10) : t < e8 ? 2 - (e2 * (t -= e5) * t + e11) : 2 - (e2 * (t -= e9) * t + e12),
  easeFromTo: (t) => (t /= half) < 1 ? half * Math.pow(t, 4) : -half * ((t -= 2) * Math.pow(t, 3) - 2),
  easeFrom: (t) => Math.pow(t, 4),
  easeTo: (t) => Math.pow(t, 0.25)
};
FunctionHelper._$name = "FunctionHelper";

// ../Core/lib/Core/Config.js
var { defineProperty, getOwnPropertyDescriptor } = Reflect;
var { hasOwnProperty: hasOwnProperty4, toString: toString2 } = Object.prototype;
var instancePropertiesSymbol = Symbol("instanceProperties");
var configuringSymbol = Symbol("configuring");
var lazyConfigValues = Symbol("lazyConfigValues");
var DATE_TYPE = toString2.call(/* @__PURE__ */ new Date());
var whitespace = /\s+/;
var hasOwn2 = Object.hasOwn || ((object, prop) => hasOwnProperty4.call(object, prop));
var dateEquals = (value1, value2) => {
  if (value1 === value2) {
    return true;
  }
  if (value1 && value2 && toString2.call(value1) === DATE_TYPE && toString2.call(value2) === DATE_TYPE) {
    return value1.getTime() === value2.getTime();
  }
  return false;
};
var getDay = (d, offset) => {
  d.setHours(0, 0, 0, 0);
  offset && d.setDate(d.getDate() + offset);
  return d;
};
var specialDates = {
  now: () => /* @__PURE__ */ new Date(),
  today: () => getDay(/* @__PURE__ */ new Date()),
  tomorrow: () => getDay(/* @__PURE__ */ new Date(), 1),
  yesterday: () => getDay(/* @__PURE__ */ new Date(), -1)
};
var createClsProps = (result, cls) => {
  result[cls] = 1;
  return result;
};
var observableGet = (name, get) => function() {
  var _a5;
  (_a5 = this.configObserver) == null ? void 0 : _a5.get(name, this);
  return get.call(this);
};
var Config = class _Config {
  /**
   * Returns the `Config` instance for the given `name` and `options`.
   * @param {String} name The name of the config (e.g., 'text' for the text config).
   * @param {Object} [options] Config behavior options.
   * @returns {Core.Config}
   * @internal
   */
  static get(name, options) {
    const { cache } = this, baseCfg = cache[name] || (cache[name] = new _Config(name));
    let cfg = baseCfg, key;
    if (options) {
      key = _Config.makeCacheKey(name, options);
      if (!(cfg = key && cache[key])) {
        cfg = baseCfg.extend(options);
        if (key) {
          cache[key] = cfg;
        }
      }
    }
    return cfg;
  }
  constructor(name) {
    const me = this, cap = name[0].toUpperCase() + name.substr(1);
    me.base = me;
    me.name = name;
    me.field = "_" + name;
    me.capName = cap;
    me.changer = "change" + cap;
    me.initializing = "initializing" + cap;
    me.updater = "update" + cap;
    me.updaterResult = `${name}Updated`;
  }
  /**
   * The descriptor to use with `Reflect.defineProperty()` for defining this config's getter and setter.
   * @property {Object}
   * @private
   */
  get descriptor() {
    let descriptor = this._descriptor;
    if (!descriptor || !hasOwn2(this, "_descriptor")) {
      this._descriptor = descriptor = this.makeDescriptor();
    }
    return descriptor;
  }
  /**
   * The descriptor to use with `Reflect.defineProperty()` for defining this config's initter.
   * @property {Object}
   * @private
   */
  get initDescriptor() {
    let descriptor = this._initDescriptor;
    if (!descriptor || !hasOwn2(this, "_initDescriptor")) {
      this._initDescriptor = descriptor = this.makeInitter();
    }
    return descriptor;
  }
  /**
   * This method converts a value presented to the setter to the value that will be processed by config hook methods
   * (e.g., `changeFoo` and `updateFoo`).
   *
   * @param {*} value The value passed to the setter.
   * @param {Core.Base} instance The instance
   * @returns {*}
   * @internal
   */
  convert(value2, instance) {
    return value2;
  }
  /**
   * This method compares two values for semantic equality. By default, this is based on the `===` operator. This
   * is often overridden for configs that accept `Date` or array values.
   * @param {*} value1
   * @param {*} value2
   * @returns {Boolean}
   * @internal
   */
  equal(value1, value2) {
    return value1 === value2;
  }
  /**
   * Extends this config with a given additional set of options. These objects are just prototype extensions of this
   * instance.
   * @param {Object} options
   * @returns {Core.Config}
   * @internal
   */
  extend(options) {
    const cfg = Object.assign(Object.create(this), options), { convert, equal, merge } = options, { convertMethods, equalityMethods } = _Config;
    if (typeof convert === "string") {
      if (!(cfg.convert = convertMethods[convert]) && convert.endsWith("[]")) {
        cfg.convert = convertMethods[convert] = _Config.makeArrayConvert(convertMethods[convert.slice(0, -2)]);
      }
    }
    if (typeof equal === "string") {
      if (!(cfg.equal = equalityMethods[equal]) && equal.endsWith("[]")) {
        cfg.equal = equalityMethods[equal] = _Config.makeArrayEquals(equalityMethods[equal.slice(0, -2)]);
      }
    }
    if (typeof merge === "string") {
      cfg.merge = _Config.mergeMethods[merge];
    }
    return cfg;
  }
  /**
   * Defines the property on a given target object via `Reflect.defineProperty()`. If the object has its own getter,
   * it will be preserved. It is invalid to define a setter.
   * @param {Object} target
   * @internal
   */
  define(target) {
    const { name } = this, existing = getOwnPropertyDescriptor(target, name);
    let descriptor = this.descriptor;
    if (existing == null ? void 0 : existing.get) {
      descriptor = Object.assign({}, descriptor);
      descriptor.get = observableGet(name, existing.get);
    }
    defineProperty(target, name, descriptor);
  }
  /**
   * Defines the property initter on the `target`. This is a property getter/setter that propagates the configured
   * value when the property is read.
   * @param {Object} target
   * @param {*} value
   * @internal
   */
  defineInitter(target, value2) {
    const me = this, { name } = me, properties = target[instancePropertiesSymbol];
    let lazyValues, prop;
    if (!properties[name] && /* assign */
    (prop = getOwnPropertyDescriptor(target, name)) && !("value" in prop)) {
      properties[name] = prop;
    }
    defineProperty(target, name, me.initDescriptor);
    if (me.lazy) {
      lazyValues = target[lazyConfigValues] || (target[lazyConfigValues] = /* @__PURE__ */ new Map());
      lazyValues.set(name, value2);
    }
  }
  static makeArrayConvert(fn2) {
    return function(value2, instance) {
      if (value2 != null) {
        if (Array.isArray(value2)) {
          value2 = value2.map((v, i) => fn2.call(this, v, instance));
        } else {
          value2 = [fn2.call(this, value2, instance)];
        }
      }
      return value2;
    };
  }
  /**
   * Returns an equality function for arrays of a base type, for example `'date'`.
   * @param {Function} [fn] The function to use to compare array elements. By default, operator `===` is used.
   * @returns {Function}
   * @private
   */
  static makeArrayEquals(fn2) {
    return (value1, value2) => {
      let i, equal = value1 && value2 && value1.length === (i = value2.length);
      if (equal && Array.isArray(value1) && Array.isArray(value2)) {
        if (fn2) {
          while (equal && i-- > 0) {
            equal = fn2(value1[i], value2[i]);
          }
        } else {
          while (equal && i-- > 0) {
            equal = value1[i] === value2[i];
          }
        }
      } else {
        equal = fn2 ? fn2(value1, value2) : value1 === value2;
      }
      return equal;
    };
  }
  /**
   * Returns the key to use in the Config `cache`.
   * @param {String} name The name of the config property.
   * @param {Object} options The config property options.
   * @returns {String}
   * @private
   */
  static makeCacheKey(name, options) {
    const keys = Object.keys(options).sort();
    for (let key, type, value2, i = keys.length; i-- > 0; ) {
      value2 = options[key = keys[i]];
      if (value2 == null && value2 === false) {
        keys.splice(i, 1);
      } else {
        type = typeof value2;
        if (type === "function") {
          return null;
        }
        if (type === "string") {
          keys[i] = `${key}:"${value2}"`;
        } else if (type === "number") {
          keys[i] = `${key}:${value2}`;
        }
      }
    }
    return keys.length ? `${name}>${keys.join("|")}` : name;
  }
  /**
   * Creates and returns a property descriptor for this config suitable to be passed to `Reflect.defineProperty()`.
   * @returns {Object}
   * @private
   */
  makeDescriptor() {
    const { base, field, changer, updater, name, updaterResult } = this;
    if (base !== this) {
      return base.descriptor;
    }
    return {
      get() {
        var _a5;
        (_a5 = this.configObserver) == null ? void 0 : _a5.get(name, this);
        return this[field];
      },
      set(value2) {
        var _a5, _b2;
        const me = this, origPassedValue = value2, config = me.$meta.configs[name];
        let was = me[field], applied, newValue;
        if (typeof value2 === "string") {
          let resolvedValue = value2;
          if (value2.startsWith("up.")) {
            resolvedValue = (_a5 = me.owner) == null ? void 0 : _a5.resolveProperty(value2.slice(3));
          } else if (value2.startsWith("this.")) {
            resolvedValue = me.resolveProperty(value2.slice(5));
          }
          if (resolvedValue !== void 0 && typeof resolvedValue !== "function") {
            value2 = resolvedValue;
          }
        }
        if (config.convert !== defaultConvert) {
          value2 = config.convert(value2, me);
        }
        if (me[changer]) {
          applied = (newValue = me[changer](value2, was)) === void 0 && 1;
          if (!applied) {
            value2 = newValue;
            was = me[field];
          }
        }
        if (!applied && !(config.equal === strictEquals ? was === value2 : config.equal(was, value2))) {
          me[field] = value2;
          applied = 2;
          me[updaterResult] = (_b2 = me[updater]) == null ? void 0 : _b2.call(me, value2, was);
        }
        if (applied && !me.isDestroyed && !me.onConfigChange.$nullFn) {
          me.onConfigChange({ name, value: value2, was, config, applied, origPassedValue });
        }
      }
    };
  }
  /**
   * Creates and returns a property descriptor for this config's initter suitable to pass to
   * `Reflect.defineProperty()`.
   * @returns {Object}
   * @private
   */
  makeInitter() {
    const config = this;
    if (config !== config.base) {
      if (config.lazy) {
        return config.makeLazyInitter();
      }
      return config.base.initDescriptor;
    }
    return config.makeBasicInitter();
  }
  makeBasicInitter() {
    const config = this, { initializing, name } = config;
    return {
      configurable: true,
      get() {
        const me = this;
        config.removeInitter(me);
        me[initializing] = true;
        me[name] = me[configuringSymbol][name];
        me[initializing] = false;
        me.configDone[name] = true;
        return me[name];
      },
      set(value2) {
        config.removeInitter(this);
        this.configDone[name] = true;
        this[name] = value2;
      }
    };
  }
  makeLazyInitter() {
    const config = this, { initializing, name } = config;
    return {
      configurable: true,
      get() {
        const me = this, value2 = me[lazyConfigValues].get(name);
        config.removeInitter(me);
        if (!me.isDestroying) {
          me[initializing] = true;
          me[name] = value2;
          me[initializing] = false;
        }
        return me[name];
      },
      set(value2) {
        config.removeInitter(this);
        this[name] = value2;
      }
    };
  }
  /**
   * Removes the property initter and restores the instance to its original form.
   * @param {Object} instance
   * @private
   */
  removeInitter(instance) {
    const { name } = this, instanceProperty = instance[instancePropertiesSymbol][name], lazyValues = instance[lazyConfigValues];
    if (instanceProperty) {
      defineProperty(instance, name, instanceProperty);
    } else {
      delete instance[name];
    }
    if ((lazyValues == null ? void 0 : lazyValues.delete(name)) && !lazyValues.size) {
      delete instance[lazyConfigValues];
    }
  }
  setDefault(cls, value2) {
    defineProperty(cls.prototype, this.field, {
      configurable: true,
      writable: true,
      // or else "this._value = x" will fail
      value: value2
    });
  }
  /**
   * This method combines (merges) two config values. This is called in two cases:
   *
   *  - When a derived class specifies the value of a config defined in a super class.
   *  - When a value is specified in the instance config object.
   *
   * @param {*} newValue In the case of derived classes, this is the config value of the derived class. In the case
   * of the instance config, this is the instance config value.
   * @param {*} currentValue In the case of derived classes, this is the config value of the super class. In the case
   * of the instance config, this is the class config value.
   * @param {Object} metaNew The class meta object from which the `newValue` is coming. This parameter is `null` if
   * the `newValue` is from an instance configuration.
   * @param {Object} metaCurrent The class meta object from which the `currentValue` is coming. This parameter is
   * `null` if the `currentValue` is not from a class configuration.
   * @returns {*}
   * @internal
   */
  merge(newValue, currentValue) {
    if (currentValue && newValue && Objects.isObject(newValue)) {
      if (currentValue.isBase) {
        return currentValue.setConfig(newValue);
      }
      if (Objects.isObject(currentValue)) {
        newValue = Objects.merge(Objects.clone(currentValue), newValue);
      }
    }
    return newValue;
  }
};
var { prototype } = Config;
var { convert: defaultConvert, equal: strictEquals } = prototype;
Config.symbols = {
  configuring: configuringSymbol,
  instanceProperties: instancePropertiesSymbol,
  lazyConfigs: lazyConfigValues
};
Config.cache = /* @__PURE__ */ Object.create(null);
Config.convertMethods = {
  array: Config.makeArrayConvert(defaultConvert),
  date(value2, instance) {
    let ret = value2, converted, format2, formats2;
    if (specialDates[value2]) {
      ret = specialDates[value2]();
    } else if (value2 != null) {
      if (typeof value2 !== "string") {
        converted = new Date(value2);
      } else {
        formats2 = this.formats;
        if (formats2) {
          formats2 = formats2.flatMap((f) => f in instance ? instance[f] : f);
          for (format2 of formats2) {
            converted = format2 && Helpers_default.Date.parse(value2, format2);
            if (converted) {
              break;
            }
          }
        }
        if (!converted) {
          converted = Helpers_default.Date.parse(value2);
        }
      }
      if (isNaN(converted)) {
        if (this.strict) {
          throw new Error(`Invalid date: ${value2}`);
        }
      } else {
        ret = converted;
      }
    }
    return ret;
  },
  day(value2, instance) {
    value2 = Config.convertMethods.date.call(this, value2, instance);
    value2 == null ? void 0 : value2.setMinutes(0, 0, 0);
    return value2;
  }
};
Config.equalityMethods = {
  array: Config.makeArrayEquals(),
  date: dateEquals,
  day: dateEquals,
  strict: Config.equal = strictEquals
};
Config.mergeMethods = {
  distinct(newValue, oldValue) {
    let ret = oldValue ? oldValue.slice() : [];
    if (newValue != null) {
      if (Objects.isObject(newValue)) {
        if (oldValue === void 0) {
          ret = newValue;
        } else {
          let key, index;
          for (key in newValue) {
            index = ret.indexOf(key);
            if (newValue[key]) {
              if (index < 0) {
                ret.push(key);
              }
            } else if (index > -1) {
              ret.splice(index, 1);
            }
          }
        }
      } else if (Array.isArray(newValue)) {
        newValue.forEach((v) => !ret.includes(v) && ret.push(v));
      } else if (!ret.includes(newValue)) {
        ret.push(newValue);
      }
    }
    return ret;
  },
  merge: Config.merge = prototype.merge,
  classList(newValue, oldValue) {
    if (typeof newValue === "string") {
      if (!newValue.length) {
        return oldValue;
      }
      newValue = newValue.split(whitespace);
    }
    if (Array.isArray(newValue)) {
      newValue = newValue.reduce(createClsProps, {});
    }
    return Config.merge(newValue, oldValue);
  },
  objects(newValue, oldValue) {
    return newValue === true ? oldValue || {} : Config.merge(newValue, oldValue);
  },
  replace(newValue) {
    return newValue;
  },
  items(newValue, oldValue, metaNew, metaCurrent) {
    if (metaCurrent) {
      return Objects.mergeItems(oldValue, newValue, {
        merge: (oldValue2, newValue2) => prototype.merge(newValue2, oldValue2)
      });
    }
    return prototype.merge(newValue, oldValue);
  }
};
Object.assign(prototype, {
  _descriptor: null,
  _initDescriptor: null,
  /**
   * A function that compares values for equality. This test is used to determine if the `update` method should be
   * called when the setter is invoked.
   *
   * To handle `Date` values:
   * ```
   *  class Foo extends Base {
   *      static get configurable() {
   *          return {
   *              date : {
   *                  $config : {
   *                      equal : 'date'
   *                  },
   *
   *                  value : null
   *              }
   *          }
   *      }
   *
   *      updateDate(date) {
   *          // date has changed
   *      }
   *  }
   * ```
   *
   * Also useful for some configs:
   * ```
   *  class Foo extends Base {
   *      static get configurable() {
   *          return {
   *              bar : {
   *                  $config : {
   *                      equal : ObjectHelper.isEqual
   *                  },
   *
   *                  value : null
   *              }
   *          }
   *      }
   *
   *      updateBar(value) {
   *          // value has changed
   *      }
   *  }
   * ```
   * @config {Function} equal
   * @internal
   */
  /**
   * Indicates that values are Date objects.
   * @config {Boolean}
   * @default
   */
  date: false,
  /**
   * Indicates that this config property should not automatically initialize during construction. When this property
   * is set to `true`, initialization is triggered by the first use of the config property's getter.
   *
   * This property can alternatively be set to a string, in which case it can be initialized as a group using the
   * {@link Core.Base#function-triggerConfigs} method which will initialize all lazy configs with the same value for
   * this property. Note: the config will still initialize on first use if that occurs prior to the call to
   * `triggerConfigs`.
   * @config {Boolean|String}
   * @default
   * @internal
   */
  lazy: false,
  /**
   * Indicates that this config property should automatically be set to `null` on destroy.
   * @config {Boolean}
   * @default
   * @internal
   */
  nullify: false,
  /**
   * Indicates that this config participates in rendering. This has does not affect the behavior of the property
   * directly, but allows classes that perform rendering to detect which config changes will affect the rendered
   * result.
   * @config {Boolean}
   * @default
   * @internal
   */
  render: false
});
Config._$name = "Config";

// ../Core/lib/Core/Base.js
var ConfigBinding = class {
  constructor(source, target, mapping, options) {
    const me = this;
    let key, value2;
    me.destroyed = false;
    me.mapFwd = {};
    me.mapRev = {};
    me.source = source;
    me.syncing = false;
    me.target = target;
    me.sourceHook = Helpers_default.Function.after(
      source,
      "onConfigChange",
      (info) => me.onConfigChange(info, true),
      me,
      noReturn
    );
    me.targetHook = (options == null ? void 0 : options.oneway) ? null : Helpers_default.Function.after(
      target,
      "onConfigChange",
      (info) => me.onConfigChange(info, false),
      me,
      noReturn
    );
    for (key in mapping) {
      value2 = mapping[key];
      if (value2) {
        if (typeof value2 !== "string") {
          value2 = key;
        }
        me.mapFwd[key] = value2;
        me.mapRev[value2] = key;
      }
    }
    me.unbind = me.destroy.bind(me);
    me.unbind.binding = me;
  }
  destroy() {
    var _a5, _b2;
    if (!this.destroyed) {
      this.destroyed = true;
      (_a5 = this.sourceHook) == null ? void 0 : _a5.call(this);
      (_b2 = this.targetHook) == null ? void 0 : _b2.call(this);
    }
  }
  onConfigChange(info, fromSource) {
    const me = this, map2 = fromSource ? me.mapFwd : me.mapRev;
    if (map2[info.name]) {
      me.sync(fromSource);
    }
  }
  sync(fromSource) {
    fromSource = fromSource !== false;
    const me = this, [from, to, map2] = fromSource ? [me.source, me.target, me.mapFwd] : [me.target, me.source, me.mapRev];
    if (!me.syncing && !from.isDestroying && !to.isDestroying) {
      let n = 0, changes, fk, fv, tk, key, val;
      me.syncing = true;
      for (fk in map2) {
        tk = map2[fk];
        fv = from[fk];
        if (fv !== to[tk]) {
          ++n;
          (changes || (changes = {}))[key = tk] = val = fv;
        }
      }
      if (n > 1) {
        to.setConfig(changes);
      } else if (n) {
        to[key] = val;
      }
      me.syncing = false;
    }
  }
};
var MetaClass = class {
  constructor(options) {
    options && Object.assign(this, options);
  }
  getInherited(name, create = true) {
    var _a5;
    let ret = this[name];
    if (!(name in this)) {
      ret = (_a5 = this.super) == null ? void 0 : _a5.getInherited(name, create);
      if (ret || create) {
        this[name] = ret = Object.create(ret || null);
        defineProperty2(ret, "$meta", { value: this });
      }
    }
    return ret;
  }
};
var { getPrototypeOf } = Object;
var { defineProperty: defineProperty2 } = Reflect;
var hasOwn3 = /* @__PURE__ */ ((hasOwn7) => (o, p) => hasOwn7.call(o, p))(Object.prototype.hasOwnProperty);
var metaSymbol = Symbol("classMetaData");
var mixinTagSymbol = Symbol("mixinTag");
var originalConfigSymbol = Symbol("originalConfig");
var createdAtSymbol = Symbol("createdAt");
var configuringSymbol2 = Config.symbols.configuring;
var instancePropertiesSymbol2 = Config.symbols.instanceProperties;
var lazyConfigsSymbol = Config.symbols.lazyConfigs;
var defaultConfigOptions = { merge: "replace", simple: true };
var cloneObject = (o) => Helpers_default.Object.clone(o);
var emptyFn = () => {
};
var newMeta = (o) => new MetaClass(o);
var isObject = (o) => Helpers_default.Object.isObject(o);
var noReturn = { return: false };
var setupNames = {
  /* foo : 'setupFoo' */
};
var emptyObject2 = Object.freeze({});
var emptyArray = Object.freeze([]);
var configTypeRe = /^(date|day)(\[])?$/;
var Base = class _Base {
  static get isBase() {
    return true;
  }
  get isBase() {
    return true;
  }
  // defaultConfig & properties made private to not spam all other classes
  /**
   * A class property getter to add additional, special class properties.
   *
   * For example, a class adds a `declarable` class property like so:
   * ```
   *  class Something extends Base {
   *      static get declarable() {
   *          return ['extra'];
   *      }
   *
   *      static setupExtra(cls, meta) {
   *          // use cls.extra
   *      }
   *  }
   * ```
   * A derived class can then specify this property like so:
   * ```
   *  class Derived extends Something {
   *      static get extra() {
   *          // return extra information
   *      }
   *  }
   * ```
   * When the `Derived` class is initialized, the `setupExtra()` method is called and `Derived` is passed as the
   * argument. It is also the `this` pointer, but the parameter is minifiable. The second argument passed is the
   * `$meta` object for the class.
   *
   * Classes are initialized at the first occurrence of the following:
   *
   * - An instance is created
   * - The class `$meta` property is accessed
   *
   * @member {String[]} declarable
   * @static
   * @category Configuration
   * @internal
   */
  static get declarable() {
    return [
      "declarable",
      /**
       * A class property getter for the configuration properties of the class, which can be overridden by
       * configurations passed at construction time.
       *
       * Unlike a normal `static` property, this property is only ever used for the class that defines it (as in,
       * `hasOwnProperty`). It is retrieved for all classes in a class hierarchy, to gather their configs
       * individually and then combine them with those of derived classes.
       *
       * For example, a `Label` might declare a `text` config like so:
       * ```javascript
       *  class Label extends Base {
       *      static get configurable() {
       *          return {
       *              text : null
       *          };
       *      }
       *  }
       * ```
       * The `text` config is automatically inherited by classes derived from Label. By implementing
       * `get configurable()`, derived classes can change the default value of inherited configs, or define new
       * configs, or both.
       *
       * When a config property is declared in this way, the class author can also implement either of two
       * special methods that will be called when the config property is assigned a new value:
       *
       *  - `changeText()`
       *  - `updateText()`
       *
       * In the example above, the `Label` class could implement a `changeText()` method, an `updateText()`
       * method, or both. The generated property setter ensures these methods will be called when the `text`
       * property is assigned.
       *
       * The generated setter (for `text` in this example) performs the following steps:
       *
       *  - If the class defines a `changeText()` method, call it passing the new value and the current value:
       *    `changeText(newText, oldText)`.<br>
       *    Then:
       *    * If `changeText()` exits without returning a value (i.e., `undefined`), exit and do nothing
       *      further. The assumption is that the changer method has done all that is required.
       *    * Otherwise, the return value of `changeText()` replaces the incoming value passed to the setter.
       *  - If the new value (or the value returned by `changeText()`) is `!==` to the current value:
       *    * Update the stored config value in `this._text`.
       *    * If the class defines an `updateText()` method, call it passing the new value and the previous value.
       *      `updateText(newText, oldText)`
       *
       * #### Resolving a value from an owner
       * By specifying a value starting with `'up.'` for a config, the config system will resolve that value by
       * examining the ownership hierarchy. It will walk up the hierarchy looking for a property matching the name
       * (or dot separated path) after 'up.'. If one is found, the value will be read and used as the initial
       * value.
       *
       * ```javascript
       * class Parent extends Base {
       *     static get configurable() {
       *         return [
       *           'importantValue'
       *         ]
       *     }
       * }
       *
       * class Child extends Base {
       *     static get configurable() {
       *         return [
       *           'value'
       *         ]
       *     }
       * }
       *
       * const parent = new Parent({
       *     importantValue : 123
       * });
       *
       * const child = new Child({
       *     owner : parent,
       *     // Will be resolved from the owner
       *     value : 'up.importantValue'
       * });
       *
       * console.log(child.value); // logs 123
       * ```
       * Please note that this is for now a one way one time binding, the value will only be read initially and
       * not kept up to date on later changes.
       *
       * #### Value Merging
       * When a config property value is an object, the value declared by the base class is merged with values
       * declared by derived classes and the value passed to the constructor.
       * ```javascript
       *  class Example extends Base {
       *      static get configurable() {
       *          return {
       *              config : {
       *                  foo : 1,
       *                  bar : 2
       *              }
       *          };
       *      }
       *  }
       *
       *  class Example2 extends Example {
       *      static get configurable() {
       *          return {
       *              config : {
       *                  bar : 42,
       *                  zip : 'abc'
       *              }
       *          };
       *      }
       *  }
       *
       *  let ex = new Example2({
       *      config : {
       *          zip : 'xyz'
       *      }
       *  });
       * ```
       * The result of the merge would set `config` to:
       * ```javascript
       *  ex.foo = {
       *      foo : 1,    // from Example
       *      bar : 42,   // from Example2
       *      zip : 'xyz' // from constructor
       *  }
       * ```
       *
       * #### Config Options
       * Some config properties require additional options such as declarative information about the config that
       * may be useful to automate some operation. Consider a `Button`. It could declare that its `text` config
       * affects the rendered HTML by applying a `render` property to the config definition. Its base class could
       * then examine the config definition to find this property.
       *
       * To support this, config options ca be declared like so:
       * ```javascript
       *  class Button extends Widget {
       *      static get configurable() {
       *          return {
       *              text : {
       *                  value   : null,
       *                  $config : {
       *                      render : true
       *                  }
       *              }
       *          };
       *      }
       *  }
       * ```
       * The `$config` property can alternatively be just the names of the options that should be enabled (set
       * to `true`).
       *
       * For example, the following is equivalent to the above:
       * ```javascript
       *  class Button extends Widget {
       *      static get configurable() {
       *          return {
       *              text : {
       *                  value   : null,
       *                  $config : 'render'
       *              }
       *          };
       *  }
       * ```
       *
       * #### Default Value
       * It is common to set a config to a `null` value to take advantage of internal optimizations for `null`
       * values. In most cases the fact that this produces `undefined` as the actual initial value of the config
       * is acceptable. When this is not acceptable, a config can be declared like so:
       * ```javascript
       *  class Widget {
       *      static get configurable() {
       *          return {
       *              disabled : {
       *                  $config : null,
       *                  value   : null,
       *                  default : false
       *              }
       *          };
       *  }
       * ```
       * The `default` property above determines the value of the config while still gaining the benefits of
       * minimal processing due to the `null` value of the `value` property.
       * @member {Object} configurable
       * @static
       * @category Configuration
       * @internal
       */
      "configurable",
      /**
       * A class property getter for the default configuration of the class, which can be overridden by
       * configurations passed at construction time.
       *
       * Unlike a normal `static` property, this property is only ever used for the class that defines it (as in,
       * `hasOwnProperty`). It is retrieved for all classes in a class hierarchy, to gather their configs
       * individually and then combine them with those of derived classes.
       *
       * For example, a `Store` might declare its `url` config like so:
       * ```
       *  class Store extends Base {
       *      static get defaultConfig() {
       *          return {
       *              url : null
       *          };
       *      }
       *  }
       * ```
       * The `url` config is automatically inherited by classes derived from Store. By implementing
       * `get defaultConfig()`, derived classes can change the default value of inherited configs, or define new
       * configs, or both. When defining new configs, however, `configurable` is preferred.
       *
       * Config properties introduced to a class by this declaration do not participate in value merging and do
       * not get a generated setter. Config properties introduced by a base class using `configurable` can be
       * set to a different value using `defaultConfig` and in doing so, the values will be merged as appropriate
       * for `configurable`.
       *
       * @member {Object} defaultConfig
       * @static
       * @category Configuration
       * @internal
       */
      "defaultConfig",
      /**
       * A class property getter for the default values of internal properties for this class.
       * @member {Object} properties
       * @static
       * @category Configuration
       * @internal
       */
      "properties",
      /**
       * A class property getter for properties that will be applied to the class prototype.
       * @member {Object} prototypeProperties
       * @static
       * @category Configuration
       * @internal
       */
      "prototypeProperties"
    ];
  }
  /**
   * Base constructor, passes arguments to {@link #function-construct}.
   * @param {...Object} [args] Usually called with a config object, but accepts any params
   * @function constructor
   * @category Lifecycle
   * @advanced
   */
  constructor(...args) {
    const me = this, C = me.constructor;
    if (me.$meta.class !== C) {
      C.$meta.initialized = true;
    }
    me.construct(...args);
    me.afterConstruct();
    me.isConstructing = false;
  }
  /**
   * Factory version of the Base constructor. Merges all arguments to create a config object that is passed along to
   * the constructor.
   * @param {...Object} [configs] Allows passing multiple config objects
   * @returns {Core.Base} New instance
   * @private
   */
  static new(...configs) {
    configs = configs.filter((c) => c);
    configs = configs.length > 1 ? this.mergeConfigs(...configs) : configs[0];
    return new this(configs);
  }
  /**
   * Base implementation applies configuration.
   *
   * Subclasses need only implement this if they have to initialize instance specific
   * properties required by the class. Often a `construct` method is
   * unnecessary. All initialization of incoming configuration properties can be
   * done in a `set propName` implementation.
   * @param {...Object} [args] Usually called with a config object, but accepts any params
   * @category Lifecycle
   * @advanced
   */
  construct(...args) {
    if (args[0] !== null) {
      this.configure(...args);
    }
    this.afterConfigure();
  }
  /**
   * Destroys the provided objects by calling their {@link #function-destroy} method.
   * Skips empty values or objects that are already destroyed.
   *
   * ```javascript
   * Base.destroy(myButton, toolbar1, helloWorldMessageBox);
   * ```
   * @param {...Object} [args] Objects to be destroyed
   * @category Lifecycle
   * @advanced
   */
  static destroy(...args) {
    const shredder = (object) => {
      if (object == null ? void 0 : object.destroy) {
        object.destroy();
      } else if (Array.isArray(object)) {
        object.forEach(shredder);
      }
    };
    shredder(args);
  }
  /**
   * Destroys this object.
   *
   * {@advanced}
   * This is primarily accomplished by calling {@link #function-doDestroy}, however, prior to
   * calling `doDestroy`, {@link #property-isDestroying} is set to `true`. After {@link #function-doDestroy} returns,
   * {@link #property-isDestroyed} is set to `true`.
   *
   * Do not override this method in subclasses. To provide class-specific cleanup, implement {@link #function-doDestroy}
   * instead.
   * {/@advanced}
   *
   * @category Lifecycle
   */
  destroy() {
    const me = this, { id: id2 } = me;
    me.isDestroying = true;
    me.destroy = emptyFn;
    me.doDestroy();
    Object.setPrototypeOf(me, null);
    for (const key in me) {
      if (key !== "destroy" && key !== "isDestroying") {
        delete me[key];
      }
    }
    delete me[originalConfigSymbol];
    me.isDestroyed = true;
    me.id = id2;
  }
  /**
   * This method is required to help `unused` getters to survive production build process. Some tools, like angular,
   * will remove `unused` code in production build, making our side-effected getters behind, breaking code heavily.
   * @internal
   * @param getter Getter to evaluate
   */
  _thisIsAUsedExpression(getter) {
  }
  static get $$name() {
    return hasOwn3(this, "$name") && this.$name || // _$name is filled by webpack for every class (cls._$name = '...')
    hasOwn3(this, "_$name") && this._$name || this.name;
  }
  get $$name() {
    return this.constructor.$$name;
  }
  /**
   * Base implementation so that all subclasses and mixins may safely call super.startConfigure.
   *
   * This is called by the Base class before setting configuration properties, but after
   * the active initial getters have been set, so all configurations are available.
   *
   * This method allows all classes in the hierarchy to force some configs to be evaluated before others.
   * @internal
   * @category Lifecycle
   * @params {Object} config The configuration object use to set the initial state.
   */
  startConfigure(config) {
  }
  /**
   * Base implementation so that all subclasses and mixins may safely call super.finishConfigure.
   *
   * This is called by the Base class before exiting the {@link #function-configure} method.
   *
   * At this point, all configs have been applied, but the `isConfiguring` property is still set.
   *
   * This method allows all classes in the hierarchy to inject functionality
   * into the config phase.
   * @internal
   * @category Lifecycle
   * @params {Object} config The configuration object use to set the initial state.
   */
  finishConfigure(config) {
  }
  /**
   * Base implementation so that all subclasses and mixins may safely call `super.afterConfigure`. This is called by the Base class after the {@link #function-configure} method has been
   * called. At this point, all configs have been applied.
   *
   * This method allows all classes in the hierarchy to inject functionality
   * either before or after the super.afterConstruct();
   * @internal
   * @category Lifecycle
   */
  afterConfigure() {
  }
  /**
   * Base implementation so that all subclasses and mixins may safely call super.afterConstruct.
   *
   * This is called by the Base class after the {@link #function-construct} method has been
   * called.
   *
   * At this point, all configs have been applied.
   *
   * This method allows all classes in the hierarchy to inject functionality
   * either before or after the super.afterConstruct();
   * @internal
   * @function afterConstruct
   * @category Lifecycle
   */
  afterConstruct() {
  }
  /**
   * Changes the value of a numeric property of this object smoothly over the specified duration.
   * @param {String} propertyName The name of the property to change.
   * @param {Number} newValue The new value to set.
   * @param {Number} duration The duration of the change in milliseconds.
   * @param {'linear'|'easeInBack'|'easeOutBack'|'easeInOutBack'|'easeOutBounce'|'easeInOutBounce'|'easeInCirc'|'easeOutCirc'|'easeInOutCirc'|'easeInCubic'|'easeOutCubic'|'easeInOutCubic'|'easeInElastic'|'easeOutElastic'|'easeInOutElastic'|'easeInExpo'|'easeOutExpo'|'easeInOutExpo'|'easeInQuad'|'easeOutQuad'|'easeInOutQuad'|'easeInQuart'|'easeOutQuart'|'easeInOutQuart'|'easeInQuint'|'easeOutQuint'|'easeInOutQuint'|'easeInSine'|'easeOutSine'|'easeInOutSine'} [easing='linear'] The easing function to use.
   * @category Misc
   */
  animateProperty(propertyName, newValue, duration = 1e3, easing) {
    const clearing = newValue == null, oldValue = this[propertyName] || 0, delta = (newValue || 0) - oldValue, result = FunctionHelper.animate(duration, (p) => this[propertyName] = oldValue + delta * p, null, easing);
    if (clearing) {
      result.then(() => this[propertyName] = null);
    }
    return result;
  }
  /**
   * Provides a way of calling callbacks which may have been specified as the _name_ of a function
   * and optionally adds scope resolution.
   *
   * For example, if the callback is specified as a string, then if it is prefixed with `'this.'`
   * then the function is resolved in this object. This is useful when configuring listeners
   * at the class level.
   *
   * If the callback name is prefixed with `'up.'` then the ownership hierarchy is queried
   * using the `owner` property until an object with the named function is present, then the
   * named function is called upon that object.
   *
   * If a named function is not found, an error is thrown. If the function should be only called when present,
   * and may not be present, add a `?` as a suffix.
   *
   * @param {String|Function} fn The function to call, or the name of the function to call.
   * @param {Object} thisObject The `this` object of the function.
   * @param {Object[]} args The argument list to pass.
   * @category Misc
   * @advanced
   */
  callback(fn2, thisObject, args = emptyArray) {
    const { handler, thisObj } = this.resolveCallback(fn2, thisObject === "this" ? this : thisObject) || emptyObject2;
    return handler == null ? void 0 : handler.apply(thisObj, args);
  }
  resolveProperty(propertyPath) {
    let thisObj = this;
    while (thisObj) {
      if (_Base.hasConfigPath(thisObj, propertyPath, true)) {
        return Helpers_default.Object.getPath(thisObj, propertyPath);
      }
      thisObj = thisObj.owner;
    }
    return void 0;
  }
  /**
   * Provides a way of locating callbacks which may have been specified as the _name_ of a function
   * and optionally adds scope resolution.
   *
   * For example, if the callback is specified as a string, then if it is prefixed with `'this.'`
   * then the function is resolved in this object. This is useful when configuring listeners
   * at the class level.
   *
   * If the callback name is prefixed with `'up.'` then the ownership hierarchy is queried
   * using the `owner` property until an object with the named function is present, then the
   * named function is called upon that object.
   * @param {String|Function} handler The function to call, or the name of the function to call.
   * @param {Object} thisObj The `this` object of the function.
   * @param {Boolean} [enforceCallability = true] Pass `false` if the function may not exist, and a null return value is acceptable.
   * @returns {Object} `{ handler, thisObj }`
   * @category Misc
   * @advanced
   */
  resolveCallback(handler, thisObj = this, enforceCallability = true) {
    if (handler == null ? void 0 : handler.substring) {
      if (handler.endsWith("?")) {
        enforceCallability = false;
        handler = handler.substring(0, handler.length - 1);
      }
      if (handler.startsWith("up.")) {
        handler = handler.substring(3);
        for (thisObj = this.owner; thisObj && !thisObj[handler]; thisObj = thisObj.owner)
          ;
        if (!thisObj) {
          return;
        }
      } else if (handler.startsWith("this.")) {
        handler = handler.substring(5);
        thisObj = this;
      }
      if (!thisObj || !(thisObj instanceof Object)) {
        return;
      }
      handler = thisObj[handler];
    }
    if (typeof handler === "function") {
      return { handler, thisObj };
    }
    if (enforceCallability) {
      throw new Error(`No method named ${handler} on ${thisObj.$$name || "thisObj object"}`);
    }
  }
  bindCallback(inHandler, inThisObj = this) {
    if (inHandler) {
      const { handler, thisObj } = this.resolveCallback(inHandler, inThisObj);
      if (handler) {
        return handler.bind(thisObj);
      }
    }
  }
  /**
   * Delays the execution of the passed function by the passed time quantum, or if the time is omitted
   * or not a number, delays until the next animation frame. Note that this will use
   * {@link Core.mixin.Delayable#function-setTimeout} || {@link Core.mixin.Delayable#function-requestAnimationFrame}
   * if this class mixes in `Delayable`, otherwise it uses the global methods. The function will
   * be called using `this` object as its execution scope.
   * @param {Function} fn The function to call on a delay.
   * @param {Number} [delay] The number of milliseconds to delay.
   * @param {String} [name] The name of delay
   * @returns {Number} The created timeout id.
   * @private
   */
  delay(fn2, delay2, name = fn2.name || fn2) {
    fn2 = this.setTimeout ? fn2 : fn2.bind(this);
    const invoker = this.setTimeout ? this : globalThis;
    return invoker[typeof delay2 === "number" ? "setTimeout" : "requestAnimationFrame"](fn2, delay2, name);
  }
  /**
   * Classes implement this method to provide custom cleanup logic before calling `super.doDestroy()`. The general
   * pattern is as follows:
   *
   * ```javascript
   *  class Foo extends Base {
   *      doDestroy() {
   *          // perform custom cleanup
   *
   *          super.doDestroy();
   *      }
   *  }
   * ```
   *
   * This method is called by {@link #function-destroy} which also prevents multiple calls from reaching `doDestroy`.
   * Prior to calling `doDestroy`, {@link #property-isDestroying} is set to `true`. Upon return, the object is fully
   * destructed and {@link #property-isDestroyed} is set to `true`.
   *
   * Do not call this method directly. Instead call {@link #function-destroy}.
   * @category Lifecycle
   * @advanced
   */
  doDestroy() {
    const me = this, { nullify } = me.$meta;
    if (nullify) {
      for (let i = 0; i < nullify.length; ++i) {
        if (me[nullify[i].field] != null) {
          me[nullify[i].name] = null;
        }
      }
    }
  }
  /**
   * Destroys the named properties if they have been initialized, and if they have a `destroy` method.
   * Deletes the property from this object. For example:
   *
   *      this.destroyProperties('store', 'resourceStore', 'eventStore', 'dependencyStore', 'assignmentStore');
   *
   * @param {String} properties The names of the properties to destroy.
   * @internal
   * @category Lifecycle
   */
  destroyProperties(...properties) {
    var _a5, _b2;
    const me = this;
    let key;
    for (key of properties) {
      if (key in me && (!me[configuringSymbol2] || !me[configuringSymbol2][key])) {
        (_b2 = (_a5 = me[key]) == null ? void 0 : _a5.destroy) == null ? void 0 : _b2.call(_a5);
        delete me[key];
      }
    }
  }
  /**
   * Called by the Base constructor to apply configs to this instance. This must not be called.
   * @param {Object} config The configuration object from which instance properties are initialized.
   * @internal
   * @category Lifecycle
   */
  configure(config = {}) {
    const me = this, meta = me.$meta, { beforeConfigure } = me.initialConfig = config, { configs } = meta, fullConfig = me.getDefaultConfiguration();
    let cfg, key, value2;
    me.isConfiguring = true;
    Object.assign(me, me.getProperties());
    for (key in config) {
      value2 = config[key];
      cfg = configs[key];
      fullConfig[key] = cfg ? cfg.merge(value2, fullConfig[key], null, meta) : value2;
    }
    if (beforeConfigure) {
      delete fullConfig.beforeConfigure;
      beforeConfigure(me, fullConfig);
    }
    me.setConfig(me[originalConfigSymbol] = fullConfig, true);
    me.isConfiguring = false;
  }
  /**
   * Creates a binding between the specified config properties of this object and the specified `target` object.
   * Changes to these config properties of this object will be reflected to the `target` object as they occur, and
   * (optionally), vise-versa.
   *
   * Returns a function to call to remove the binding.
   *
   * @param {Core.Base} target
   * @param {Object} mapping An object whose keys are the config properties of this object to sync to the `target`.
   * Any key with a falsy value is ignored. Keys with a string value use that value as the name of the corresponding
   * config property of the `target`. Any other key with a truthy value is used as the name of the config property
   * for both this object and the `target`.
   * @param {Object} [options] Options controlling the sync process
   * @param {Object} [options.oneway=false] Pass `true` to disable syncing config property changes on the `target`
   * back to this object.
   * @param {Object} [options.sync=true] Pass `false` to disable the initial sync of config properties to the
   * `target` object.
   * @returns {Function}
   * @internal
   */
  bindConfigs(target, mapping, options) {
    const binding = new ConfigBinding(this, target, mapping, options);
    (options == null ? void 0 : options.sync) !== false && binding.sync();
    return binding.unbind;
  }
  /**
   * Returns the value of the specified config property. This is a method to allow
   * property getters to be explicitly called in a way that does not get optimized out.
   *
   * The following triggers the getter call, but optimizers will remove it:
   *
   *      inst.foo;   // also raises "expression has no side-effects" warning
   *
   * Instead, do the following to trigger a getter:
   *
   *      inst.getConfig('foo');
   *
   * @param {String} name The name of a config to (initialize and) return.
   * @param {...String} names Additional config names to return. If more than one parameter is passed, an array of
   * config values is returned. Otherwise, only the config value for `name` is returned.
   * @return {*}
   * @internal
   * @category Configuration
   */
  getConfig(name, ...names2) {
    const val = this[name];
    return names2.length ? [val, ...names2.map((n) => this[n])] : val;
  }
  /**
   * This is intended to set a block of configs *during* configuration. It is not intended to be used
   * outside of the configuration phase.
   *
   * If any of the properties are not yet ingested from the configuration object, they will be set
   * and ingested in the normal order.
   *
   * Properties which are already ingested are set immediately.
   * @param {Object} props  An object containing configurations to change.
   * @internal
   */
  updateConfiguration(props) {
    var _a5;
    const me = this, { configDone } = me, { configs } = me.$meta, configValues = me[configuringSymbol2], lazyValues = me[lazyConfigsSymbol];
    me.recompose.suspend();
    for (const key in props) {
      if (configDone[key]) {
        me[key] = props[key];
      } else {
        if ((_a5 = configs[key]) == null ? void 0 : _a5.lazy) {
          lazyValues.set(key, props[key]);
        } else {
          configValues[key] = props[key];
        }
      }
    }
    me.recompose.resume();
    me.recompose.flush();
  }
  /**
   * Sets configuration options this object with all the properties passed in the parameter object.
   * Timing is taken care of. If the setter of one config is called first, and references
   * the value of another config which has not yet been set, that config will be set just
   * in time, and the *new* value will be used.
   * @param {Object} config An object containing configurations to change.
   * @category Lifecycle
   * @advanced
   */
  setConfig(config, isConstructing = false) {
    var _a5;
    const me = this, wasConfiguring = me[configuringSymbol2], configDone = wasConfiguring ? me.configDone : me.configDone = {}, configs = me.$meta.configs, pendingLazy = me[lazyConfigsSymbol];
    let cfg, key;
    me[instancePropertiesSymbol2] = {};
    me[configuringSymbol2] = wasConfiguring ? Object.assign(Object.create(wasConfiguring), config) : config;
    for (key in config) {
      cfg = configs[key] || Config.get(key);
      if (config[key] !== null || (hasOwn3(config, key) || cfg.default === null)) {
        if (!(pendingLazy == null ? void 0 : pendingLazy.has(key))) {
          cfg.defineInitter(me, config[key]);
        }
        if (!isConstructing) {
          configDone[key] = false;
        }
      } else {
        configDone[key] = true;
      }
    }
    if (isConstructing) {
      me.startConfigure(config);
    }
    for (key in config) {
      if (!configDone[key] && !(isConstructing && ((_a5 = configs[key]) == null ? void 0 : _a5.lazy))) {
        me[key] = config[key];
      }
    }
    if (wasConfiguring) {
      me[configuringSymbol2] = wasConfiguring;
    } else {
      delete me[configuringSymbol2];
    }
    if (isConstructing) {
      me.finishConfigure(config);
    }
    return me;
  }
  /**
   * Returns `true` if this instance has a non-null value for the specified config. This will not activate a lazy
   * config.
   *
   * @param {String} name The name of the config property.
   * @returns {Boolean}
   * @internal
   */
  hasConfig(name) {
    var _a5;
    const me = this, config = me[configuringSymbol2];
    return Boolean(
      me["_" + name] != null || // value has been assigned to backing property
      ((_a5 = me[lazyConfigsSymbol]) == null ? void 0 : _a5.get(name)) != null || // a lazy value is pending
      // config value has not been assigned but will be
      !me.configDone[name] && config && (config[name] != null || hasOwn3(config, name))
    );
  }
  /**
   * Determines if the specified config property exists in an instance of {@link Core.Base}
   * @param {Object} object Object to check path on
   * @param {String} path Dot-separated path, e.g. 'object.childObject.someKey'
   * @returns {Boolean}
   * @internal
   */
  static hasConfigPath(object, path) {
    return path.split(".").every((key) => {
      var _a5, _b2, _c2;
      const config = (_b2 = (_a5 = object == null ? void 0 : object.$meta) == null ? void 0 : _a5.configs) == null ? void 0 : _b2[key], isConfig = config && config.descriptor.get === ((_c2 = Helpers_default.Object.getPropertyDescriptor(object, key)) == null ? void 0 : _c2.get);
      if (isConfig ? object.hasConfig(key) : key in object) {
        object = object[key];
        return Object !== void 0;
      }
      return false;
    });
  }
  /**
   * Returns the value of an uningested config *without* ingesting the config or transforming
   * it from its raw value using its `changeXxxxx` method.
   *
   * @param {String} name The name of the config property.
   * @returns {*} The raw incoming config value.
   * @internal
   */
  peekConfig(name) {
    const me = this, lazyConfig = me[lazyConfigsSymbol], config = me[configuringSymbol2];
    if (lazyConfig == null ? void 0 : lazyConfig.has(name)) {
      return lazyConfig.get(name);
    }
    if (config && name in config) {
      if (me.configDone[name]) {
        return me[name];
      }
      if (config[name] != null || hasOwn3(config, name)) {
        return config[name];
      }
    }
  }
  /**
   * Ensures that the specified config is initialized if it is needed. If there is a config value specified, and it
   * was initialized by this call, this method returns `true`. If there was a config value specified, and it was
   * already initialized, this method returns `false`. If there was no value specified for the given config, this
   * method returns `null`.
   *
   * This is not the same as just reading the property, because some property getters exist that do not actually just
   * read the config value back, but instead produce some result. Reading such properties to incidentally trigger a
   * possible config initializer can lead to incorrect results. For example, the Combo items config.
   *
   * @param {String} name The name of the config property.
   * @returns {Boolean}
   * @internal
   */
  triggerConfig(name) {
    const me = this, { configDone } = me, lazyConfig = me[lazyConfigsSymbol], config = me[configuringSymbol2], triggered = (lazyConfig == null ? void 0 : lazyConfig.has(name)) || config && (config[name] != null || hasOwn3(config, name)) ? !configDone[name] : null;
    if (triggered) {
      me.getConfig(name);
    }
    return triggered;
  }
  /**
   * This call will activate any pending {@link Core.Config#config-lazy} configs that were assigned a string value
   * equal to the `group` parameter.
   *
   * @param {String} group The config property group as defined by a matching {@link Core.Config#config-lazy} value.
   * @returns {String[]} The names of any configs triggered by this call or `null` if no configs were triggered.
   * @internal
   */
  triggerConfigs(group) {
    const me = this, configs = me.$meta.configs, lazyConfigs = me[lazyConfigsSymbol], triggered = lazyConfigs ? [...lazyConfigs.keys()].filter((k) => configs[k].lazy === group) : emptyArray;
    for (const key of triggered) {
      me.triggerConfig(key);
    }
    return triggered.length ? triggered : null;
  }
  onConfigChange() {
  }
  // declared above because lint/IDE get angry about not declaring the args...
  /**
   * This method is called when any config changes.
   * @param {Object} info Object containing information regarding the config change.
   * @param {String} info.name The name of the config that changed.
   * @param {*} info.value The new value of the config.
   * @param {*} info.was The previous value of the config.
   * @param {Core.Config} info.config The `Config` object for the changed config property.
   * @method onConfigChange
   * @internal
   * @category Configuration
   */
  /**
   * Returns a *copy* of the full configuration which was used to configure this object.
   * @property {Object}
   * @category Lifecycle
   * @readonly
   * @advanced
   */
  get config() {
    const result = {}, myConfig = this[originalConfigSymbol];
    for (const key in myConfig) {
      result[key] = myConfig[key];
    }
    return result;
  }
  // region Extract config
  static processConfigValue(currentValue, options) {
    if (currentValue === globalThis) {
      return globalThis;
    } else if (Array.isArray(currentValue)) {
      return currentValue.map((v) => _Base.processConfigValue(v, options));
    } else if (currentValue instanceof _Base) {
      if (options.visited.has(currentValue)) {
        return;
      }
      return currentValue.getCurrentConfig(options);
    } else if (currentValue instanceof HTMLElement || currentValue instanceof DocumentFragment) {
      return null;
    } else if (isObject(currentValue)) {
      const result = {};
      for (const key in currentValue) {
        if (key !== "owner") {
          result[key] = _Base.processConfigValue(currentValue[key], options);
        }
      }
      return result;
    }
    return currentValue;
  }
  // Recursively get the value of a config. Only intended to be called by getCurrentConfig()
  getConfigValue(name, options) {
    var _a5;
    const me = this, lazyConfigs = me[lazyConfigsSymbol];
    if (!((_a5 = me.$meta.configs[name]) == null ? void 0 : _a5.lazy)) {
      return _Base.processConfigValue(me[name], options);
    }
    if (lazyConfigs == null ? void 0 : lazyConfigs.has(name)) {
      return _Base.processConfigValue(lazyConfigs.get(name), options);
    }
  }
  // Allows removing / adding configs before values are extracted
  preProcessCurrentConfigs() {
  }
  // Extract the current values for all initially used configs, in a format that can be used to create a new instance.
  // Not intended to be called by any other code than getConfigString()
  getCurrentConfig(options = {}) {
    const me = this, configs = options.configs === "all" ? me.config : cloneObject(me.initialConfig), skip = options.skip || {}, visited = options.visited || (options.visited = /* @__PURE__ */ new Set()), depth = options.depth || (options.depth = 0), result = {};
    if (visited.has(me)) {
      return void 0;
    }
    visited.add(me);
    this.preProcessCurrentConfigs(configs);
    for (const name in configs) {
      if (skip[name]) {
        continue;
      }
      const value2 = me.getConfigValue(name, { ...options, depth: depth + 1 });
      if (value2 !== void 0) {
        result[name] = value2;
      }
    }
    return result;
  }
  // Extract the current values for all initially used configs and convert them to a JavaScript string
  getConfigString(options = {}) {
    return Helpers_default.String.toJavaScriptString(this.getCurrentConfig(options));
  }
  // Experimental helper function, extracts the currently used configs and wraps them as an app, returning code as a
  // string.
  //
  // This function is intended to simplify creating test cases for issue reporting on Bryntum's support forum.
  //
  getTestCase(options = {}) {
    const Product = this.isGantt ? "Gantt" : this.isSchedulerPro ? "SchedulerPro" : this.isCalendar ? "Calendar" : this.isScheduler ? "Scheduler" : this.isGrid ? "Grid" : this.isTaskBoard ? "TaskBoard" : null;
    if (Product) {
      const product = Product.toLowerCase(), bundlePath = `../../build/${product}.module.js`;
      let preamble, postamble;
      if (options.import === "static") {
        preamble = `import * as module from "${bundlePath}";Object.assign(window, module);`;
        postamble = "";
      } else {
        preamble = `import("${bundlePath}").then(module => { Object.assign(window, module);
`;
        postamble = "});";
      }
      const version = VersionHelper.getVersion(product);
      if (version) {
        preamble += `
console.log('${Product} ${version}');
`;
      }
      return `${preamble}      
const ${product} = new ${Product}(${this.getConfigString(options)});
${postamble}`;
    }
  }
  /**
   * Experimental helper function, extracts the currently used configs and wraps them as an app, downloading the
   * resulting JS file.
   *
   * This function is intended to simplify creating test cases for issue reporting on Bryntum's support forum.
   * @category Misc
   */
  downloadTestCase(options = {}) {
    options.output = "return";
    const app = this.getTestCase(options);
    Helpers_default.Browser.download(`app.js`, "data:application/javascript;charset=utf-8," + escape(app));
  }
  //endregion
  /**
   * Registers this class type with its Factory
   * @category Misc
   * @advanced
   */
  static initClass() {
    return this.$meta.class;
  }
  /**
   * The class's {@link #property-$meta-static meta} object.
   * @member {Object} $meta
   * @internal
   * @category Misc
   */
  /**
   * An object owned by this class that does not share properties with its super class.
   *
   * This object may contain other properties which are added as needed and are not documented here.
   *
   * @property {Object} $meta The class meta object.
   * @property {Function} $meta.class The class constructor that owns the meta object.
   * @property {Object} $meta.super The `$meta` object for the super class. This is `null` for `Base`.
   * @property {Object} $meta.config The object holding the default configuration values for this class.
   * @property {Object} $meta.configs An object keyed by config name that holds the defined configs for the class.
   * The value of each property is a {@link Core/Config} instance.
   * @property {Boolean} $meta.forkConfigs This will be `true` if the default configuration values for this class
   * (in the `config` property of the meta object) must be forked to avoid object sharing, or if the object can be
   * passed to `Object.create()` for efficiency.
   * @property {Function[]} $meta.hierarchy The array of classes in the ancestry of this class. This will start with
   * `Base` at index 0 and ends with this class.
   * @property {Function[]} $meta.properties The array of classes that define a "static get properties()" getter.
   * @internal
   * @static
   * @category Misc
   */
  static get $meta() {
    if (!hasOwn3(this, metaSymbol)) {
      const meta = this[metaSymbol] = new MetaClass();
      meta.class = this;
      this.setupClass(meta);
      return meta;
    }
    return this[metaSymbol];
  }
  /**
   * This optional class method is called when a class is mixed in using the {@link #function-mixin-static mixin()}
   * method.
   * @internal
   */
  static onClassMixedIn() {
  }
  /**
   * Returns the merge of the `baseConfig` and `config` config objects based on the configs defined by this class.
   * @param {Object} baseConfig The base config or defaults.
   * @param {...Object} configs One or more config objects that takes priority over `baseConfig`.
   * @returns {Object}
   * @internal
   */
  static mergeConfigs(baseConfig, ...configs) {
    const classConfigs = this.$meta.configs, result = cloneObject(baseConfig) || {};
    let config, i, key, value2;
    for (i = 0; i < configs.length; ++i) {
      config = configs[i];
      if (config) {
        for (key in config) {
          value2 = config[key];
          if (classConfigs[key]) {
            value2 = classConfigs[key].merge(value2, result[key]);
          } else if (result[key] && value2) {
            value2 = Config.merge(value2, result[key]);
          }
          result[key] = value2;
        }
      }
    }
    return result;
  }
  /**
   * Applies one or more `mixins` to this class and returns the produced class constructor.
   *
   * For example, instead of writing this:
   * ```
   *  class A extends Delayable(Events(Localizable(Base))) {
   *      // ...
   *  }
   * ```
   *
   * Using this method, one would write this:
   * ```
   *  class A extends Base.mixin(Localizable, Events, Delayable) {
   *      // ...
   *  }
   * ```
   * If one of the mixins specified has already been mixed into the class, it will be ignored and not mixed in a
   * second time.
   * @param {...Function} mixins
   * @returns {Function}
   * @category Misc
   * @advanced
   */
  static mixin(...mixins) {
    let C = this, i;
    for (i = 0; i < mixins.length; ++i) {
      const mixin2 = mixins[i], tag = mixin2[mixinTagSymbol] || (mixin2[mixinTagSymbol] = Symbol("mixinTag"));
      if (C[tag]) {
        continue;
      }
      C = mixin2(C);
      C[tag] = true;
      if (hasOwn3(C, "onClassMixedIn")) {
        C.onClassMixedIn();
      }
    }
    return C;
  }
  /**
   * This method is called only once for any class. This can occur when the first instance is created or when the
   * `$meta` object is first requested.
   * @param {Object} meta The `$meta` object for the class.
   * @internal
   * @category Misc
   */
  static setupClass(meta) {
    var _a5;
    const cls = meta.class, base = getPrototypeOf(cls).$meta, name = cls.$$name, names2 = base.names, proto4 = cls.prototype;
    defineProperty2(proto4, "$meta", {
      value: meta
    });
    Object.assign(meta, {
      super: base,
      config: Object.create(base.config),
      configs: Object.create(base.configs),
      declarables: base.declarables,
      forkConfigs: base.forkConfigs,
      hierarchy: Object.freeze([...base.hierarchy, cls]),
      names: names2.includes(name) ? names2 : Object.freeze([...names2, name]),
      properties: base.properties,
      nullify: (_a5 = base.nullify) == null ? void 0 : _a5.slice()
    });
    if (names2 !== meta.names) {
      const isName = `is${name}`, defineIsProperty = (obj) => {
        if (!hasOwn3(obj, isName)) {
          defineProperty2(obj, isName, {
            get() {
              return true;
            }
          });
        }
      };
      defineIsProperty(proto4);
      defineIsProperty(cls);
    }
    for (let decl, setupName, i = 0; i < meta.declarables.length; ++i) {
      decl = meta.declarables[i];
      if (hasOwn3(cls, decl)) {
        setupName = setupNames[decl] || (setupNames[decl] = `setup${Helpers_default.String.capitalize(decl)}`);
        cls[setupName](cls, meta);
      }
    }
  }
  /**
   * This method is called as part of `setupClass()`. It will process the `configurable()` return object and the
   * `defaultConfig` return object.
   * @param {Object} meta The `meta` object for this class.
   * @param {Object} configs The config definition object.
   * @param {Boolean} simple `true` when processing `defaultConfig` and `false` when processing `configurable`.
   * @private
   * @category Configuration
   */
  static setupConfigs(meta, configs, simple) {
    const classConfigValues = meta.config, classConfigs = meta.configs, cls = meta.class, superMeta = meta.super;
    let { nullify } = meta, cfg, defaultValue2, options, setDefault, value2, wasNullify;
    for (const name in configs) {
      value2 = configs[name];
      if (simple) {
        if (!(cfg = classConfigs[name])) {
          cfg = Config.get(name, defaultConfigOptions);
        } else {
          value2 = cfg.merge(value2, classConfigValues[name], meta, superMeta);
        }
      } else {
        defaultValue2 = options = setDefault = void 0;
        if (value2 && typeof value2 === "object" && "$config" in value2) {
          options = value2.$config;
          if (configTypeRe.test(String(options))) {
            options = {
              type: options
            };
          }
          if (options) {
            if (!isObject(options)) {
              options = Helpers_default.Object.createTruthyKeys(options);
            } else if (options.type) {
              options = {
                convert: options.type,
                equal: options.type,
                ...options
              };
              delete options.type;
            }
          }
          setDefault = "default" in value2;
          defaultValue2 = setDefault ? value2.default : defaultValue2;
          value2 = "value" in value2 ? value2.value : null;
        }
        if (!(cfg = classConfigs[name])) {
          cfg = Config.get(name, options);
          cfg.define(cls.prototype);
          setDefault = !(cfg.field in cls.prototype);
          wasNullify = false;
        } else {
          wasNullify = cfg.nullify;
          if (options) {
            cfg = cfg.extend(options);
          }
          value2 = cfg.merge(value2, classConfigValues[name], meta, superMeta);
        }
        if (setDefault) {
          cfg.setDefault(cls, defaultValue2);
        }
        if (cfg.nullify && !wasNullify) {
          (nullify || (nullify = meta.nullify || (meta.nullify = []))).push(cfg);
        }
      }
      if (value2 && (isObject(value2) || Array.isArray(value2)) && !Object.isFrozen(value2)) {
        meta.forkConfigs = true;
      }
      classConfigs[name] = cfg;
      classConfigValues[name] = value2;
    }
  }
  static setupConfigurable(cls, meta) {
    cls.setupConfigs(meta, cls.configurable, false);
  }
  static setupDefaultConfig(cls, meta) {
    cls.setupConfigs(meta, cls.defaultConfig, true);
  }
  static setupDeclarable(cls, meta) {
    const declarable = cls.declarable;
    let all = meta.declarables, forked, i;
    for (i = 0; i < declarable.length; ++i) {
      if (!all.includes(declarable[i])) {
        if (!forked) {
          meta.declarables = forked = all = all.slice();
        }
        all.push(declarable[i]);
      }
    }
  }
  static setupProperties(cls, meta) {
    meta.properties = meta.super.properties.slice();
    meta.properties.push(cls);
    Object.freeze(meta.properties);
  }
  static setupPrototypeProperties(cls) {
    Object.assign(cls.prototype, cls.prototypeProperties);
  }
  static fork(obj) {
    let ret = obj, key, value2;
    if (obj && isObject(obj) && !Object.isFrozen(obj)) {
      ret = Object.create(obj);
      for (key in obj) {
        value2 = obj[key];
        if (value2) {
          if (isObject(value2)) {
            ret[key] = _Base.fork(value2);
          } else if (Array.isArray(value2)) {
            ret[key] = value2.slice();
          }
        }
      }
    }
    return ret;
  }
  /**
   * Gets the full {@link #property-defaultConfig-static} block for this object's entire inheritance chain
   * all the way up to but not including {@link Core.Base}
   * @returns {Object} All default config values for this class.
   * @internal
   * @category Configuration
   */
  static getDefaultConfiguration() {
    return this.$meta.class.prototype.getDefaultConfiguration();
  }
  /**
   * Gets the full {@link #property-defaultConfig-static} block for the entire inheritance chain for this class
   * all the way up to but not including {@link Core.Base}
   * @returns {Object} All default config values for this class.
   * @internal
   * @category Configuration
   */
  getDefaultConfiguration() {
    const meta = this.$meta, config = meta.forkConfigs ? _Base.fork(meta.config) : Object.create(meta.config);
    if (VersionHelper.isTestEnv && Helpers_default.Browser.isBrowserEnv && config.testConfig && globalThis.__applyTestConfigs) {
      for (const o in config.testConfig) {
        config[o] = config.testConfig[o];
      }
    }
    return config;
  }
  /**
   * Gets the full {@link #property-properties-static} block for this class's entire inheritance chain
   * all the way up to but not including {@link Core.Base}
   * @returns {Object} All default config values for this class.
   * @private
   * @category Configuration
   */
  getProperties() {
    const hierarchy = this.$meta.properties, result = {};
    for (let i = 0; i < hierarchy.length; i++) {
      const desc = Object.getOwnPropertyDescriptor(hierarchy[i], "properties");
      Object.assign(result, desc.get !== void 0 ? hierarchy[i].properties : cloneObject(desc.value));
    }
    return result;
  }
  /**
   * Merges the provided default configuration values with the existing defaults for this class (and its subclasses).
   *
   * This method allows you to define additional or override existing default property values
   * that apply to all instances of the class.
   *
   * ```javascript
   * // All my input fields should be clearable
   *  Field.applyDefaults({
   *      clearable : true
   * });
   * ```
   * @param {Object<String, *>} defaultsObj An object with the default values to set.
   * @category Configuration
   * @advanced
   */
  static applyDefaults(defaultsObj) {
    for (const key in defaultsObj) {
      this.$meta.config[key] = defaultsObj[key];
    }
  }
  static get superclass() {
    return getPrototypeOf(this);
  }
  /**
   * Used by the Widget and GridFeatureManager class internally. Returns the class hierarchy of this object
   * starting from the `topClass` class (which defaults to `Base`).
   *
   * For example `classHierarchy(Widget)` on a Combo would yield `[Widget, Field, TextField, PickerField, Combo]`
   * @param {Function} [topClass] The topmost class constructor to start from.
   * @returns {Function[]} The class hierarchy of this instance.
   * @private
   * @category Configuration
   */
  classHierarchy(topClass) {
    const hierarchy = this.$meta.hierarchy, index = topClass ? hierarchy.indexOf(topClass) : 0;
    return index > 0 ? hierarchy.slice(index) : hierarchy;
  }
  /**
   * Checks if an obj is of type using object's $$name property and doing string comparison of the property with the
   * type parameter.
   *
   * @param {String} type
   * @returns {Boolean}
   * @category Misc
   * @advanced
   */
  static isOfTypeName(type) {
    return this.$meta.names.includes(type);
  }
  /**
   * Removes all event listeners that were registered with the passed names.
   * @param {...String|Symbol} name The name(s) of the event listeners to be removed.
   * @category Events
   * @advanced
   */
  detachListeners(...names2) {
    const { $detachers } = this;
    if ($detachers) {
      for (let i = 0, { length } = names2, detacher2; i < length; i++) {
        if (detacher2 = $detachers[names2[i]]) {
          while (detacher2.length) {
            detacher2.pop()();
          }
        }
      }
    }
  }
  /**
   * Tracks a detacher function for the specified listener name.
   * @param {String} name The name assigned to the associated listeners.
   * @param {Function} detacher The detacher function.
   * @private
   */
  trackDetacher(name, detacher2) {
    const detachers = this.$detachers || (this.$detachers = {}), bucket = detachers[name] || (detachers[name] = []);
    bucket.push(detacher2);
  }
  /**
   * Removes all detacher functions for the specified `Events` object. This is called
   * by the `removeAllListeners` method on that object which is typically called by its
   * `destroy` invocation.
   * @param {Core.mixin.Events} eventer The `Events` instance to untrack.
   * @private
   */
  untrackDetachers(eventer) {
    const detachers = this.$detachers;
    if (detachers) {
      for (const name in detachers) {
        const bucket = detachers[name];
        for (let i = bucket.length; i-- > 0; ) {
          if (bucket[i].eventer === eventer) {
            bucket.splice(i, 1);
          }
        }
      }
    }
  }
};
var proto = Base.prototype;
proto.onConfigChange.$nullFn = emptyFn.$nullFn = true;
Base[metaSymbol] = proto.$meta = newMeta({
  class: Base,
  config: Object.freeze({}),
  configs: /* @__PURE__ */ Object.create(null),
  declarables: Base.declarable,
  forkConfigs: false,
  hierarchy: Object.freeze([Base]),
  names: Object.freeze(["Base"]),
  nullify: null,
  properties: Object.freeze([]),
  super: null
});
Object.assign(proto, {
  $detachers: null,
  configObserver: null,
  /**
   * This property is set to `true` before the `constructor` returns.
   * @member {Boolean}
   * @readonly
   * @category Lifecycle
   * @advanced
   */
  isConstructing: true,
  /**
   * This property is set to `true` by {@link #function-destroy} after destruction.
   *
   * It is also one of the few properties that remains on the object after returning from `destroy()`. This property
   * is often checked in code paths that may encounter a destroyed object (like some event handlers) or in the
   * destruction path during cleanup.
   *
   * @member {Boolean}
   * @readonly
   * @category Lifecycle
   */
  isDestroyed: false,
  /**
   * This property is set to `true` on entry to the {@link #function-destroy} method. It remains on the objects after
   * returning from `destroy()`. If {@link #property-isDestroyed} is `true`, this property will also be `true`, so
   * there is no need to test for both (for example, `comp.isDestroying || comp.isDestroyed`).
   * @member {Boolean}
   * @readonly
   * @category Lifecycle
   * @advanced
   */
  isDestroying: false
});
Base.emptyFn = emptyFn;
VersionHelper.setVersion("core", "6.2.5");
Base._$name = "Base";

// ../Core/lib/Core/helper/ArrayHelper.js
var _ArrayHelper = class _ArrayHelper {
  static clean(array) {
    return array.reduce((res, item) => {
      if (item !== null && item !== void 0 && !(Array.isArray(item) && item.length === 0) && item !== "")
        res.push(item);
      return res;
    }, []);
  }
  /**
   * Similar to [`Array.from()`](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/from)
   * this method creates an array from an `iterable` object. Where `Array.from()` accepts a mapper function as the
   * second argument, this method accepts a `filter` function as its second argument. If a mapper function is also
   * needed, it can be passed as the third argument. Unlike `Array.from()`, if this method is passed `null`, it will
   * return an empty array.
   * @param {Array} iterable The iterable object to convert (must support `for-of` loop iteration).
   * @param {Function} [filter] A function to apply to each item of the `iterable` which must return a truthy value
   * to include that item in the resulting array.
   * @param {Function} [map] A function to apply to each item of the `iterable` that returns the actual value to put
   * into the returned array. If a `filter` is also supplied, this method is only called for those items that pass
   * the filter test.
   * @returns {Array}
   */
  static from(iterable, filter2, map2) {
    const array = [];
    if (iterable) {
      for (const it of iterable) {
        if (!filter2 || filter2(it)) {
          array.push(map2 ? map2(it) : it);
        }
      }
    }
    return array;
  }
  /**
   * Remove one or more items from an array
   * @param {Array} array Array to remove from
   * @param {Object[]|Set} items One or more items to remove, or one Set containing items to remove
   * @returns {Boolean} Returns true if any item was removed
   */
  static remove(array, ...items2) {
    let index, item, removed = false;
    items2 = items2[0] instanceof Set ? [...items2[0]] : items2;
    for (let i = 0; i < items2.length; i++) {
      item = items2[i];
      if ((index = array.indexOf(item)) !== -1) {
        array.splice(index, 1);
        removed = true;
      }
    }
    return removed;
  }
  /**
   * Calculates the insertion index of a passed object into the passed Array according
   * to the passed comparator function. Note that the passed Array *MUST* already be ordered.
   * @param {Object} item The item to calculate the insertion index for.
   * @param {Array} array The array into which the item is to be inserted.
   * @param {Function} comparatorFn The comparison function. Must return -1 or 0 or 1.
   * @param {Object} comparatorFn.lhs The left object to compare.
   * @param {Object} comparatorFn.rhs The right object to compare.
   * @param {Number} index The possible correct index to try first before a binary
   * search is instigated.
   * @internal
   */
  static findInsertionIndex(item, array, comparatorFn = this.lexicalCompare, index) {
    const len = array.length;
    let beforeCheck, afterCheck;
    if (index < len) {
      beforeCheck = index > 0 ? comparatorFn(array[index - 1], item) : 0;
      afterCheck = index < len - 1 ? comparatorFn(item, array[index]) : 0;
      if (beforeCheck < 1 && afterCheck < 1) {
        return index;
      }
    }
    return this.binarySearch(array, item, comparatorFn);
  }
  /**
   * Similar to the native `Array.find()` call, but this finds the *last* element in the array for which
   * the passed function returns a truthy value.
   * @param {Object[]} array The array to find in.
   * @param {Function} fn The testing function.
   * @param {Object} [thisObj] The scope (`this` reference) in which to call the function.
   */
  static findLast(array, fn2, thisObj) {
    for (let { length } = array, i = length - 1; i >= 0; i--) {
      if (fn2.call(thisObj, array[i], i, array)) {
        return array[i];
      }
    }
  }
  /**
   * This method returns the index that a given item would be inserted into the
   * given (sorted) `array`. Note that the given `item` may or may not be in the
   * array. This method will return the index of where the item *should* be.
   *
   * For example:
   *
   *      var array = [ 'A', 'D', 'G', 'K', 'O', 'R', 'X' ];
   *      var index = ArrayHelper.binarySearch(array, 'E');
   *
   *      console.log('index: ' + index);
   *      // logs "index: 2"
   *
   *      array.splice(index, 0, 'E');
   *
   *      console.log('array : ' + array.join(''));
   *      // logs "array: ADEGKORX"
   *
   * @param {Object[]} array The array to search.
   * @param {Object} item The item that you want to insert into the `array`.
   * @param {Number} [begin=0] The first index in the `array` to consider.
   * @param {Number} [end=array.length] The index that marks the end of the range
   * to consider. The item at this index is *not* considered.
   * @param {Function} [compareFn] The comparison function that matches the sort
   * order of the `array`. The default `compareFn` compares items using less-than
   * and greater-than operators.
   * @returns {Number} The index for the given item in the given array based on
   * the passed `compareFn`.
   */
  static binarySearch(array, item, begin = 0, end = array.length, compareFn = this.lexicalCompare) {
    const length = array.length;
    let middle, comparison;
    if (begin instanceof Function) {
      compareFn = begin;
      begin = 0;
    } else if (end instanceof Function) {
      compareFn = end;
      end = length;
    }
    --end;
    while (begin <= end) {
      middle = begin + end >> 1;
      comparison = compareFn(item, array[middle]);
      if (comparison >= 0) {
        begin = middle + 1;
      } else if (comparison < 0) {
        end = middle - 1;
      }
    }
    return begin;
  }
  magnitudeCompare(lhs, rhs) {
    return lhs < rhs ? -1 : lhs > rhs ? 1 : 0;
  }
  lexicalCompare(lhs, rhs) {
    lhs = String(lhs);
    rhs = String(rhs);
    return lhs < rhs ? -1 : lhs > rhs ? 1 : 0;
  }
  /**
   * Similar to Array.prototype.fill(), but constructs a new array with the specified item count and fills it with
   * clones of the supplied item.
   * @param {Number} count Number of entries to create
   * @param {Object|Array} itemOrArray Item or array of items to clone (uses object spread to create shallow clone)
   * @param {Function} [fn] An optional function that is called for each item added, to allow processing
   * @returns {Array} A new populated array
   */
  static fill(count, itemOrArray = {}, fn2 = null) {
    const result = [], items2 = Array.isArray(itemOrArray) ? itemOrArray : [itemOrArray];
    for (let i = 0; i < count; i++) {
      for (const item of items2) {
        const processedItem = Object.assign({}, item);
        if (fn2) {
          fn2(processedItem, i);
        }
        result.push(processedItem);
      }
    }
    return result;
  }
  /**
   * Populates an array with the return value from `fn`.
   * @param {Number} count Number of entries to create
   * @param {Function} fn A function that is called `count` times, return value is added to array
   * @param {Number} fn.index Current index in the array
   * @privateparam {Boolean} [oneBased] Add 1 to the index before calling the fn (making it 1 based)
   * @returns {Array} A new populated array
   */
  static populate(count, fn2, oneBased = false) {
    const items2 = [];
    for (let i = 0; i < count; i++) {
      items2.push(fn2(i + (oneBased ? 1 : 0)));
    }
    return items2;
  }
  /**
   * Pushes `item` on to the `array` if not already included
   * @param {Array}  array Array to push to
   * @param {...Object} items Item(s) to push if not already included
   */
  static include(array, ...items2) {
    for (const item of items2) {
      if (!array.includes(item)) {
        array.push(item);
      }
    }
  }
  /**
   * Returns a new array with the unique items from the supplied array.
   * @param {Array} array Input array
   * @returns {Array} New array with unique items
   */
  static unique(array) {
    return [...new Set(array)];
  }
  /**
   * Returns an array of unique values and their occurrence count, in { value, count } format
   * @param {Array} array Input array
   * @returns {Array} New array with unique items and counts in { value, count } format
   */
  static countUnique(array) {
    const counts = /* @__PURE__ */ new Map();
    for (const value2 of array) {
      const count = counts.get(value2);
      counts.set(value2, count === void 0 ? 1 : count + 1);
    }
    return [...counts.entries()].map(([value2, count]) => ({ value: value2, count }));
  }
  // Kept for future reference : Wanted to create an indexer on Stores.
  static allowNegative(array) {
    return new Proxy(array, {
      get(target, name, receiver) {
        if (typeof name !== "string") {
          return Reflect.get(target, name, receiver);
        }
        const index = Number(name);
        if (Number.isNaN(index)) {
          return Reflect.get(target, name, receiver);
        }
        return target[index < 0 ? target.length + index : index];
      },
      set(target, name, value2, receiver) {
        if (typeof name !== "string") {
          return Reflect.set(target, name, value2, receiver);
        }
        const index = Number(name);
        if (Number.isNaN(index)) {
          return Reflect.set(target, name, value2, receiver);
        }
        target[index < 0 ? target.length + index : index] = value2;
        return true;
      }
    });
  }
  static delta(a, b, useRelativeNaming = false) {
    if (!b) {
      return useRelativeNaming ? { toAdd: a, toRemove: [], toKeep: [] } : { onlyInA: a, onlyInB: [], inBoth: [] };
    }
    const onlyInA = [], onlyInB = [], inBoth = /* @__PURE__ */ new Set(), bSet = new Set(b);
    for (let i = 0; i < a.length; i++) {
      const item = a[i];
      if (bSet.has(item)) {
        inBoth.add(item);
      } else {
        onlyInA.push(item);
      }
    }
    for (let i = 0; i < b.length; i++) {
      const item = b[i];
      if (!inBoth.has(item)) {
        onlyInB.push(item);
      }
    }
    if (useRelativeNaming) {
      return { toAdd: onlyInA, toRemove: onlyInB, toKeep: inBoth };
    }
    return { onlyInA, onlyInB, inBoth: [...inBoth] };
  }
  /**
   * Returns the passed object wrapped in an array. Special handling of the following cases:
   * * Passing an array returns it as is
   * * Passing a `Set` returns it converted to an Array
   * * Passing `null`/`undefined` returns the passed value
   *
   * ```javascript
   * const records = ArrayHelper.asArray(record);
   *
   * // { id : 1 } -> [{ id : 1 }]
   * // [{ id : 1 }] -> [{ id : 1 }]
   * ```
   *
   * @param {*} arrayOrObject
   * @returns {Array|null}
   * @internal
   */
  static asArray(arrayOrObject) {
    if (arrayOrObject == null) {
      return arrayOrObject;
    }
    if (arrayOrObject instanceof Set) {
      return Array.from(arrayOrObject);
    }
    return Array.isArray(arrayOrObject) ? arrayOrObject : [arrayOrObject];
  }
  /**
   * Identity function that returns its input.
   * @param {*} Any value
   * @returns {*} The input value
   * @internal
   */
  static identity(x) {
    return x;
  }
  /**
   * Transform an array into a key:value dictionary using the specified
   * key and value getters. Does not group values, so only one result will
   * appear in the output for a given key.
   *
   * ```javascript
   * const input = [{
   *     id: '1',
   *     other: 'one'
   * },{
   *     id: '2',
   *     other: 'two'
   * }];
   *
   * keyBy(input, rec => rec.id)
   *
   * // {
   * //    '1': { id: '1', other: 'one' },
   * //    '2': { id: '2', other: 'two' }
   * // }
   * ```
   *
   * @param {Array} array Array from which to build dictionary
   * @param {Function|String} getKey Function to produce the key for a given array entry, or a string property name to read
   * @param {Function} getValue Optional function to transform array elements. Defaults to returning the element itself
   * @returns {Object} A key->value dictionary with keys as returned by `getKey` and values returned by `getValue`
   * @internal
   */
  static keyBy(array, keyGetter, valueGetter = _ArrayHelper.identity) {
    const getKey = typeof keyGetter === "string" ? (o) => o == null ? void 0 : o[keyGetter] : keyGetter, getValue = typeof valueGetter === "string" ? (o) => o == null ? void 0 : o[valueGetter] : valueGetter;
    return array.reduce((dict, next) => {
      dict[getKey(next)] = getValue(next);
      return dict;
    }, {});
  }
  /**
   * Combines provided arrays of by aggregating their element values.
   * For example the below code sums up numeric elements of the arrays:
   *
   * ```javascript
   * ArrayHelper.aggregate(
   *     [
   *         [0,   1,  2, 33]
   *         [10,  1, -1],
   *         [100, 1, -1]
   *     ],
   *     entry => entry || 0, // "|| 0" here to make it work for different array sizes
   *     (aggregated, entry) => aggregated + entry, // aggregate by summing up
   *     () => 0 //initial value is zero
   * );
   *
   * // returns [111, 3, 0, 33] array
   * ```
   *
   * @param {Array[]} arrays Arrays to combine
   * @param {Function} getEntryValueFn Function that extracts an array entry for aggregating.
   * @param {Function} aggregatorFn A function to execute for each element in the arrays. It's purpose is to
   * aggregate the element value to the corresponding entry of the resulting array.
   * The function's return value becomes the value of the `aggregated` parameter on the next invocation of
   * `aggregatorFn`.
   * The function is called with the following arguments:
   *
   * @param {Object} aggregatorFn.aggregated Resulting array entry value. On the first call
   * `getInitialValueFn` result.
   * @param {Object} aggregatorFn.entry Current entry to aggregate into `aggregated`.
   * @param {Number} aggregatorFn.arrayIndex Index of current array (in the provided `arrays`).
   * @param {Object[]} aggregatorFn.entryIndex Index of the current entry.
   * @param {Object} aggregationContext A shared object providing extra aggregation call context.
   * @param {Function} getInitialValueFn Function that returns an initial value for the combined array entries.
   * @param {Object} [aggregationContext] Optional object that is passed to all of the above functions that can
   * be used for keeping some additional parameters used when aggregating.
   * Out of the box the object will contain `arrays` and `targetArray` properties containing
   * input and resulting arrays respectively.
   * @returns {Array} Resulting array of combined values.
   */
  static aggregate(arrays, getEntryValueFn, aggregatorFn, getInitialValueFn, aggregationContext = {}) {
    const rowLength = arrays.length, result = [];
    if (!rowLength) {
      return result;
    }
    const columnLength = arrays[0].length;
    aggregationContext.targetArray = result;
    aggregationContext.arrays = arrays;
    for (let colIndex = 0; colIndex < columnLength; colIndex++) {
      aggregationContext.entryIndex = colIndex;
      result.push(getInitialValueFn(colIndex, aggregationContext));
    }
    for (let rowIndex = 0; rowIndex < rowLength; rowIndex++) {
      const row = arrays[rowIndex];
      aggregationContext.rowIndex = rowIndex;
      for (let colIndex = 0; colIndex < columnLength; colIndex++) {
        aggregationContext.entryIndex = colIndex;
        const entryValue = getEntryValueFn(row[colIndex], aggregationContext);
        result[colIndex] = aggregatorFn(result[colIndex], entryValue, rowIndex, colIndex, aggregationContext);
      }
    }
    return result;
  }
  /**
   * Group an array by keys (either the values in a specified property name, or the results of a string-generating function accepting
   * an array entry as input), returning an Object with those keys, whose values are arrays containing the array entries that
   * produced that key.
   *
   * ```javascript
   * const input = [{
   *     id: 1,
   *     color: 'red'
   * },{
   *     id: 2,
   *     color: 'green'
   * },{
   *     id: 3,
   *     color: 'green'
   * }];
   *
   * groupBy(input, 'color')
   *
   * // {
   * //    'red': [ { id: '1', color: 'red' } ],
   * //    'green': [ { id: '2', color: 'green' }, { id: '3', color: 'green' } ]
   * // }
   *
   * groupBy(input, rec => rec.color?.substr(0, 1))
   *
   * // {
   * //    'r': [ { id: '1', color: 'red' } ],
   * //    'g': [ { id: '2', color: 'green' }, { id: '3', color: 'green' } ]
   * // }
   * ```
   *
   * @param {Object} array Array from which to build dictionary
   * @param {Function|String} getGroupKey Function to produce the key for a given array entry, or a string property name to read
   * @returns {Object} A key->value[] dictionary with keys as returned by `getKey` and arrays of matching items in original order as values
   * @internal
   */
  static groupBy(array, groupKeyGetter) {
    const getKey = typeof groupKeyGetter === "string" ? (o) => o == null ? void 0 : o[groupKeyGetter] : groupKeyGetter;
    return array.reduce((grouped, value2) => {
      const key = getKey(value2);
      (grouped[key] = grouped[key] || []).push(value2);
      return grouped;
    }, {});
  }
  static groupByIndexed(array, getKey) {
    return array.reduce((grouped, value2) => {
      const key = getKey(value2);
      let existing = grouped.get(key);
      if (existing === void 0) {
        existing = /* @__PURE__ */ new Set();
        grouped.set(key, existing);
      }
      existing.add(value2);
      return grouped;
    }, /* @__PURE__ */ new Map());
  }
  /**
   * Splice items into an array in chunks, to avoid exceeding the maximum call stack size
   * @param {Array} array Array to splice into
   * @param {Number} start Starting index to splice at
   * @param {Array} itemsToAdd Array of items to add to the array
   * @param {Number} [stepSize=50000] Number of items to add in each step, defaults to 50 000
   * @internal
   */
  static chunkedInsert(array, start, itemsToAdd, stepSize = 5e4) {
    const steps = itemsToAdd.length / stepSize;
    for (let i = 0; i < steps; i++) {
      const startIndex = i * stepSize, endIndex = (i + 1) * stepSize;
      array.splice(start + startIndex, 0, ...itemsToAdd.slice(startIndex, endIndex));
    }
  }
  /**
   * Push items into an array, avoiding exceeding the maximum call stack size.
   *
   * @param {Array} array Array to push into
   * @param {Array} itemsToAdd Array of items to add to the array
   * @internal
   */
  static chunkedPush(array, itemsToAdd) {
    for (const item of itemsToAdd) {
      array.push(item);
    }
  }
};
__publicField(_ArrayHelper, "$name", "ArrayHelper");
var ArrayHelper = _ArrayHelper;
Helpers_default.register(ArrayHelper);
ArrayHelper._$name = "ArrayHelper";

// ../Core/lib/Core/mixin/Events.js
var { isArray } = Array;
var { hasOwnProperty: hasOwnProperty5 } = Object.prototype;
var specialProperties = {
  thisObj: 1,
  detachable: 1,
  once: 1,
  detacher: 1,
  prio: 1,
  args: 1,
  expires: 1,
  buffer: 1,
  throttle: 1,
  name: 1,
  $internal: 1
};
var priorityComparator = (a, b) => b.prio - a.prio;
var mergeListeners = (newValue, currentValue) => {
  if (newValue !== null) {
    if (!newValue) {
      return currentValue;
    }
    if (currentValue) {
      newValue = newValue ? [newValue] : [];
      newValue.push[isArray(currentValue) ? "apply" : "call"](newValue, currentValue);
    }
  }
  return newValue;
};
var Events_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    constructor() {
      super(...arguments);
      __publicField(this, "eventsSuspended", null);
    }
    //region Events
    /**
     * Fires before an object is destroyed.
     * @event beforeDestroy
     * @param {Core.Base} source The Object that is being destroyed.
     */
    /**
     * Fires when an object is destroyed.
     * @event destroy
     * @param {Core.Base} source The Object that is being destroyed.
     */
    /**
     * Fires when any other event is fired from the object.
     *
     * **Note**: `catchAll` is fired for both public and private events. Please rely on the public events only.
     * @event catchAll
     * @param {Object} event The Object that contains event details
     * @param {String} event.type The type of the event which is caught by the listener
     * @typings event -> {{[key: string]: any, type: string}}
     */
    //endregion
    static get declarable() {
      return [
        /**
         * The list of deprecated events as an object, where `key` is an event name which is deprecated and
         * `value` is an object which contains values for
         * {@link Core.helper.VersionHelper#function-deprecate-static VersionHelper}:
         * - product {String} The name of the product;
         * - invalidAsOfVersion {String} The version where the offending code is invalid (when any compatibility
         *   layer is actually removed);
         * - message {String} Warning message to show to the developer using a deprecated API;
         *
         * For example:
         *
         * ```javascript
         * return {
         *     click : {
         *         product            : 'Grid',
         *         invalidAsOfVersion : '1.0.0',
         *         message            : 'click is deprecated!'
         *     }
         * }
         * ```
         *
         * @name deprecatedEvents
         * @returns {Object}
         * @static
         * @internal
         */
        "deprecatedEvents"
      ];
    }
    static setupDeprecatedEvents(cls, meta) {
      const all = meta.getInherited("deprecatedEvents"), add = cls.deprecatedEvents;
      for (const eventName in add) {
        all[eventName.toLowerCase()] = all[eventName] = add[eventName];
      }
    }
    //region Config
    static get configurable() {
      return {
        /**
         * Set to `true` to call onXXX method names (e.g. `onShow`, `onClick`), as an easy way to listen for events.
         *
         * ```javascript
         * const container = new Container({
         *     callOnFunctions : true
         *
         *     onHide() {
         *          // Do something when the 'hide' event is fired
         *     }
         * });
         * ```
         *
         * @config {Boolean} callOnFunctions
         * @category Misc
         * @default false
         */
        /**
         * The listener set for this object.
         *
         * An object whose property names are the names of events to handle, or options which modifiy
         * __how__ the handlers are called.
         *
         * See {@link #function-addListener} for details about the options.
         *
         * Listeners can be specified in target class config and they will be merged with any listeners specified in
         * the instantiation config. Class listeners will be fired first:
         *
         * ```javascript
         * class MyStore extends Store({
         *     static get configurable() {
         *         return {
         *             listeners : {
         *                 myCustomEvent() {
         *                 },
         *                 load : {
         *                     prio : 10000,
         *                     fn() { // this load listener handles things first }
         *                 }
         *             }
         *         }
         *     }
         * });
         *
         * let store = new MyStore({
         *   listeners: {
         *     load: () => { // This load listener runs after the class's },
         *     ...
         *   }
         * });
         * ```
         *
         * ### Handlers as function name
         *
         * Object event handlers may be specified as a function __name__. If a string is specified, it is the name
         * of the function in the `thisObj` object.
         *
         * If the string begins with `up.`, this object's ownership hierarchy
         * (if present) is scanned for an object which implements that function name:
         *
         * ```javascript
         * new Popup({
         *     tbar : {
         *         items : {
         *             myCombo : {
         *                 type      : 'combo',
         *                 editable  : false,
         *                 label     : 'Type',
         *                 listeners : {
         *                     // Look in owner chain for this function name
         *                     change : 'up.onFilterChange'
         *                 },
         *                 items     : [
         *                     'Event',
         *                     'Task',
         *                     'Appointment'
         *                 ]
         *             }
         *         }
         *     },
         *     items : {
         *         ...
         *     },
         *     onFilterChange({ value }) {
         *         // Handle event type selection here
         *     }
         * });
         * ```
         *
         * @config {Object}
         * @category Common
         */
        listeners: {
          value: null,
          $config: {
            merge: mergeListeners
          }
        },
        /**
         * Internal listeners, that cannot be removed by the user.
         * @config {Object}
         * @internal
         */
        internalListeners: {
          value: null,
          $config: {
            merge: mergeListeners
          }
        },
        /**
         * An object where property names with a truthy value indicate which events should bubble up the ownership
         * hierarchy when triggered.
         *
         * ```javascript
         * const container = new Container({
         *     items : [
         *        { type : 'text', bubbleEvents : { change : true }}
         *     ],
         *
         *     listeners : {
         *         change() {
         *             // Will catch change event from the text field
         *         }
         *     }
         * });
         * ```
         *
         * @config {Object}
         * @category Misc
         */
        bubbleEvents: null
      };
    }
    destroy() {
      this.trigger("beforeDestroy");
      super.destroy();
    }
    //endregion
    //region Init
    construct(config, ...args) {
      if (this.configuredListeners = config == null ? void 0 : config.listeners) {
        config = Objects.assign({}, config);
        delete config.listeners;
      }
      super.construct(config, ...args);
      this.processConfiguredListeners();
    }
    processConfiguredListeners() {
      if (this.configuredListeners) {
        const me = this, { isConfiguring } = me;
        me.isConfiguring = false;
        me.listeners = me.configuredListeners;
        me.configuredListeners = null;
        me.isConfiguring = isConfiguring;
      }
    }
    /**
     * Auto detaches listeners registered from start, if set as detachable
     * @internal
     */
    doDestroy() {
      this.trigger("destroy");
      this.removeAllListeners(false);
      super.doDestroy();
    }
    static setupClass(meta) {
      super.setupClass(meta);
      _a5.prototype.onListen.$nullFn = true;
      _a5.prototype.onUnlisten.$nullFn = true;
    }
    //endregion
    //region Listeners
    /**
     * Adds an event listener. This method accepts parameters in the following format:
     *
     * ```javascript
     *  myObject.addListener({
     *      thisObj    : this,          // The this reference for the handlers
     *      eventname2 : 'functionName' // Resolved at invocation time using the thisObj,
     *      otherevent : {
     *          fn      : 'handlerFnName',
     *          once    : true          // Just this handler is auto-removed on fire
     *      },
     *      yetanother  : {
     *          fn      : 'yetAnotherHandler',
     *          args    : [ currentState1, currentState2 ] // Capture info to be passed to handler
     *      },
     *      prio        : 100           // Higher prio listeners are called before lower
     *  });
     * ```
     *
     * When listeners have a `thisObj` option, they are linked to the lifecycle of that object.
     * When it is destroyed, those listeners are removed.
     *
     * The `config` parameter allows supplying options for the listener(s), for available options see {@link #typedef-BryntumListenerConfig}.
     *
     * A simpler signature may be used when only adding a listener for one event and no extra options
     * (such as `once` or `delay`) are required:
     *
     * ```javascript
     * myObject.addListener('click', myController.handleClicks, myController);
     * ```
     *
     * The args in this simple case are `eventName`, `handler` and `thisObj`
     *
     * @param {BryntumListenerConfig|String} config An object containing listener definitions, or the event name to listen for
     * @param {Object|Function} [thisObj] Default `this` reference for all listeners in the config object, or the handler
     * function to call if providing a string as the first arg.
     * @param {Object} [oldThisObj] The `this` reference if the old signature starting with a string event name is used..
     * @returns {Function} Returns a detacher function unless configured with `detachable: false`. Call detacher to remove listeners
     */
    addListener(config, thisObj, oldThisObj) {
      var _a6;
      if (isArray(config)) {
        for (let i = 0, { length } = config; i < length; i++) {
          this.addListener(config[i], thisObj);
        }
        return;
      }
      const me = this, deprecatedEvents = me.$meta.getInherited("deprecatedEvents");
      if (typeof config === "string") {
        return me.addListener({
          [config]: thisObj,
          detachable: thisObj.detachable !== false,
          thisObj: oldThisObj
        });
      } else {
        thisObj = config.thisObj = config.thisObj !== void 0 ? config.thisObj : thisObj;
        for (const key in config) {
          if (!specialProperties[key] && config[key] != null) {
            const eventName = key.toLowerCase(), deprecatedEvent = deprecatedEvents == null ? void 0 : deprecatedEvents[eventName], events = me.eventListeners || (me.eventListeners = {}), specs = ArrayHelper.asArray(config[key]);
            if (deprecatedEvent) {
              const { product, invalidAsOfVersion, message } = deprecatedEvent;
              VersionHelper.deprecate(product, invalidAsOfVersion, message);
            }
            for (const listenerSpec of specs) {
              const expires = listenerSpec.expires || config.expires, listener = {
                fn: typeof listenerSpec === "object" ? listenerSpec.fn : listenerSpec,
                thisObj: listenerSpec.thisObj !== void 0 ? listenerSpec.thisObj : thisObj,
                args: listenerSpec.args || config.args,
                prio: listenerSpec.prio !== void 0 ? listenerSpec.prio : config.prio !== void 0 ? config.prio : 0,
                once: listenerSpec.once !== void 0 ? listenerSpec.once : config.once !== void 0 ? config.once : false,
                buffer: listenerSpec.buffer || config.buffer,
                throttle: listenerSpec.throttle || config.throttle,
                $internal: config.$internal,
                catchAll: key === "catchAll"
              };
              if (expires) {
                const { alt: alt2 } = expires, delay2 = alt2 ? expires.delay : expires, name2 = config.name || key, fn2 = () => {
                  me.un(eventName, listener);
                  if (alt2 && !listener.called) {
                    me.callback(alt2, thisObj);
                  }
                };
                if (me.isDelayable) {
                  me.setTimeout({
                    fn: fn2,
                    name: name2,
                    cancelOutstanding: true,
                    delay: delay2
                  });
                } else {
                  globalThis.setTimeout(fn2, delay2);
                }
              }
              let listeners = events[eventName] || (events[eventName] = []);
              if (listeners.$firing) {
                events[eventName] = listeners = listeners.slice();
              }
              listeners.splice(
                ArrayHelper.findInsertionIndex(listener, listeners, priorityComparator, listeners.length),
                0,
                listener
              );
              if (!me.onListen.$nullFn && listeners.length < 2) {
                me.onListen(eventName);
              }
              (_a6 = me.afterAddListener) == null ? void 0 : _a6.call(me, eventName, listener);
            }
          }
        }
        if (config.relayAll) {
          me.relayAll(config.relayAll);
        }
        if (thisObj && thisObj !== me) {
          me.attachAutoDetacher(config, thisObj);
        }
        const detachable = config.detachable !== false, name = config.name, destroy = config.expires || detachable || name ? () => {
          if (!me.isDestroyed) {
            me.removeListener(config, thisObj);
          }
        } : null;
        if (destroy) {
          destroy.eventer = me;
          destroy.listenerName = name;
          if (name && (thisObj == null ? void 0 : thisObj.trackDetacher)) {
            thisObj.trackDetacher(name, destroy);
          }
          if (config.expires) {
            me.delay(destroy, isNaN(config.expires) ? config.expires.delay : config.expires, name);
          }
          if (detachable) {
            return destroy;
          }
        }
      }
    }
    /**
     * Alias for {@link #function-addListener}. Adds an event listener. This method accepts parameters in the following format:
     *
     * ```javascript
     *  myObject.on({
     *      thisObj    : this,          // The this reference for the handlers
     *      eventname2 : 'functionName' // Resolved at invocation time using the thisObj,
     *      otherevent : {
     *          fn      : 'handlerFnName',
     *          once    : true          // Just this handler is auto-removed on fire
     *      },
     *      yetanother  : {
     *          fn      : 'yetAnotherHandler',
     *          args    : [ currentState1, currentState2 ] // Capture info to be passed to handler
     *      },
     *      prio        : 100           // Higher prio listeners are called before lower
     *  });
     * ```
     *
     * When listeners have a `thisObj` option, they are linked to the lifecycle of that object.
     * When it is destroyed, those listeners are removed.
     *
     * The `config` parameter allows supplying options for the listener(s), for available options see {@link #typedef-BryntumListenerConfig}.
     *
     * A simpler signature may be used when only adding a listener for one event and no extra options
     * (such as `once` or `delay`) are required:
     *
     * ```javascript
     * myObject.on('click', myController.handleClicks, myController);
     * ```
     *
     * The args in this simple case are `eventName`, `handler` and `thisObj`
     *
     * @param {BryntumListenerConfig|String} config An object containing listener definitions, or the event name to listen for
     * @param {Object|Function} [thisObj] Default `this` reference for all listeners in the config object, or the handler
     * function to call if providing a string as the first arg.
     * @param {Object} [oldThisObj] The `this` reference if the old signature starting with a string event name is used..
     * @returns {Function} Returns a detacher function unless configured with `detachable: false`. Call detacher to remove listeners
     */
    on(config, thisObj, oldThisObj) {
      return this.addListener(config, thisObj, oldThisObj);
    }
    /**
     * Internal convenience method for adding an internal listener, that cannot be removed by the user.
     *
     * Alias for `on({ $internal : true, ... })`. Only supports single argument form.
     *
     * @internal
     */
    ion(config) {
      config.$internal = true;
      return this.on(config);
    }
    /**
     * Shorthand for {@link #function-removeListener}
     * @param {Object|String} config A config object or the event name
     * @param {Object|Function} [thisObj] `this` reference for all listeners, or the listener function
     * @param {Object} [oldThisObj] `this` The `this` object for the legacy way of adding listeners
     */
    un(...args) {
      this.removeListener(...args);
    }
    updateInternalListeners(internalListeners, oldInternalListeners) {
      oldInternalListeners == null ? void 0 : oldInternalListeners.detach();
      if (internalListeners) {
        internalListeners.detach = this.ion(internalListeners);
      }
    }
    get listeners() {
      return this.eventListeners;
    }
    changeListeners(listeners) {
      if (this.isConfiguring) {
        this.getConfig("internalListeners");
        if (listeners) {
          this.on(listeners, this);
        }
      } else {
        if (Array.isArray(listeners)) {
          for (let i = 0, l = listeners[0], { length } = listeners; i < length; l = listeners[++i]) {
            if (!("thisObj" in l)) {
              listeners[i] = Objects.assign({ thisObj: this }, l);
            }
          }
        } else if (listeners && !("thisObj" in listeners)) {
          listeners = Objects.assign({ thisObj: this }, listeners);
        }
        return listeners;
      }
    }
    updateListeners(listeners, oldListeners) {
      oldListeners && this.un(oldListeners);
      listeners && this.on(listeners);
    }
    /**
     * Removes an event listener. Same API signature as {@link #function-addListener}
     * @param {Object|String} config A config object or the event name
     * @param {Object|Function} thisObj `this` reference for all listeners, or the listener function
     * @param {Object} oldThisObj `this` The `this` object for the legacy way of adding listeners
     */
    removeListener(config, thisObj = config.thisObj, oldThisObj) {
      var _a6;
      const me = this;
      if (typeof config === "string") {
        return me.removeListener({ [config]: thisObj }, oldThisObj);
      }
      for (let eventName in config) {
        const listenerToRemove = config[eventName];
        if (!specialProperties[eventName] && listenerToRemove != null) {
          eventName = eventName.toLowerCase();
          const { eventListeners } = me, index = me.findListener(eventName, listenerToRemove, thisObj);
          if (index >= 0) {
            let listeners = eventListeners[eventName];
            (_a6 = me.afterRemoveListener) == null ? void 0 : _a6.call(me, eventName, listeners[index]);
            if (listeners.length > 1) {
              if (listeners.$firing) {
                eventListeners[eventName] = listeners = listeners.slice();
              }
              listeners.splice(index, 1);
            } else {
              delete eventListeners[eventName];
              if (!me.onUnlisten.$nullFn) {
                me.onUnlisten(eventName);
              }
            }
          }
        }
      }
      ;
      if (config.thisObj && !config.thisObj.isDestroyed) {
        me.detachAutoDetacher(config);
      }
    }
    /**
     * Finds the index of a particular listener to the named event. Returns `-1` if the passed
     * function/thisObj listener is not present.
     * @param {String} eventName The name of an event to find a listener for.
     * @param {String|Function} listenerToFind The handler function to find.
     * @param {Object} defaultThisObj The `thisObj` for the required listener.
     * @internal
     */
    findListener(eventName, listenerToFind, defaultThisObj) {
      var _a6;
      const eventListeners = (_a6 = this.eventListeners) == null ? void 0 : _a6[eventName], fn2 = listenerToFind.fn || listenerToFind, thisObj = listenerToFind.thisObj || defaultThisObj;
      if (eventListeners) {
        for (let listenerEntry, i = 0, { length } = eventListeners; i < length; i++) {
          listenerEntry = eventListeners[i];
          if (listenerEntry.fn === fn2 && listenerEntry.thisObj === thisObj) {
            return i;
          }
        }
      }
      return -1;
    }
    /**
     * Check if any listener is registered for the specified eventName
     * @param {String} eventName
     * @returns {Boolean} `true` if listener is registered, otherwise `false`
     * @advanced
     */
    hasListener(eventName) {
      var _a6;
      return Boolean((_a6 = this.eventListeners) == null ? void 0 : _a6[eventName == null ? void 0 : eventName.toLowerCase()]);
    }
    /**
     * Relays all events through another object that also implements Events mixin. Adds a prefix to the event name
     * before relaying, for example add -> storeAdd
     * ```
     * // Relay all events from store through grid, will make it possible to listen for store events prefixed on grid:
     * 'storeLoad', 'storeChange', 'storeRemoveAll' etc.
     * store.relayAll(grid, 'store');
     *
     * grid.on('storeLoad', () => console.log('Store loaded');
     * ```
     * @param {Core.mixin.Events} through Object to relay the events through, needs to mix Events mixin in
     * @param {String} prefix Prefix to add to event name
     * @param {Boolean} [transformCase] Specify false to prevent making first letter of event name uppercase
     * @advanced
     */
    relayAll(through, prefix, transformCase = true) {
      if (!this.relayAllTargets) {
        this.relayAllTargets = [];
      }
      const { relayAllTargets } = this;
      through.ion({
        beforeDestroy: ({ source }) => {
          if (source === through) {
            const configs = relayAllTargets.filter((r) => r.through === through);
            configs.forEach((config) => ArrayHelper.remove(relayAllTargets, config));
          }
        }
      });
      relayAllTargets.push({ through, prefix, transformCase });
    }
    /**
     * Removes all listeners registered to this object by the application.
     */
    removeAllListeners(preserveInternal = true) {
      var _a6;
      const listeners = this.eventListeners;
      let i, thisObj;
      for (const event in listeners) {
        const bucket = listeners[event];
        for (i = bucket.length; i-- > 0; ) {
          const cfg = bucket[i];
          if (!cfg.$internal || !preserveInternal) {
            this.removeListener(event, cfg);
            thisObj = cfg.thisObj;
            (_a6 = thisObj == null ? void 0 : thisObj.untrackDetachers) == null ? void 0 : _a6.call(thisObj, this);
          }
        }
      }
    }
    relayEvents(source, eventNames, prefix = "", relayerAsSource) {
      const listenerConfig = { detachable: true, thisObj: this };
      eventNames.forEach((eventName) => {
        if (prefix) {
          eventName = StringHelper.capitalize(eventName);
        }
        listenerConfig[eventName] = (event, ...params) => {
          if (relayerAsSource) {
            event = Object.assign({}, event, { source: this });
          }
          return this.trigger(prefix + eventName, event, ...params);
        };
      });
      return source.on(listenerConfig);
    }
    /**
     * This method is called when the first listener for an event is added.
     * @param {String} eventName
     * @internal
     */
    onListen() {
    }
    /**
     * This method is called when the last listener for an event is removed.
     * @param {String} eventName
     * @internal
     */
    onUnlisten() {
    }
    destructorInterceptor() {
      const { autoDetachers, target, oldDestructor } = this;
      for (let i = 0; i < autoDetachers.length; i++) {
        const { dispatcher, config } = autoDetachers[i];
        if (!dispatcher.isDestroyed) {
          dispatcher.removeListener(config, target);
        }
      }
      oldDestructor.call(target);
    }
    /**
     * Internal function used to hook destroy() calls when using thisObj
     * @private
     */
    attachAutoDetacher(config, thisObj) {
      const target = config.thisObj || thisObj, destructorName = "doDestroy" in target ? "doDestroy" : "destroy";
      if (destructorName in target) {
        let { $autoDetachers } = target;
        if (!$autoDetachers) {
          target.$autoDetachers = $autoDetachers = [];
        }
        if (!target.$oldDestructor) {
          target.$oldDestructor = target[destructorName];
          target[destructorName] = this.destructorInterceptor.bind({
            autoDetachers: $autoDetachers,
            oldDestructor: target.$oldDestructor,
            target
          });
        }
        $autoDetachers.push({ config, dispatcher: this });
      } else {
        target[destructorName] = () => {
          this.removeListener(config);
        };
      }
    }
    /**
     * Internal function used restore hooked destroy() calls when using thisObj
     * @private
     */
    detachAutoDetacher(config) {
      const target = config.thisObj;
      if (target.$oldDestructor && !target.isDestroying) {
        ArrayHelper.remove(
          target.$autoDetachers,
          target.$autoDetachers.find((detacher2) => detacher2.config === config && detacher2.dispatcher === this)
        );
        if (!target.$autoDetachers.length) {
          target["doDestroy" in target ? "doDestroy" : "destroy"] = target.$oldDestructor;
          target.$oldDestructor = null;
        }
      }
    }
    /**
     * Internal function used to run a callback function after an event is triggered
     * @private
     */
    once(eventName, callback) {
      return this.ion({
        [eventName]: (event) => this.callback(callback, this, [event]),
        once: true
      });
    }
    //endregion
    //region Promise based workflow
    // experimental, used in tests to support async/await workflow
    await(eventName, options = { checkLog: true, resetLog: true, args: null, expires: null }) {
      const me = this;
      if (options === false) {
        options = { checkLog: false };
      }
      const { args } = options;
      return new Promise((resolve) => {
        var _a6;
        if (options.checkLog && ((_a6 = me._triggered) == null ? void 0 : _a6[eventName])) {
          resolve();
          if (options.resetLog) {
            me.clearLog(eventName);
          }
        }
        if (args) {
          const detacher2 = me.on({
            [eventName]: (...params) => {
              const argsOk = typeof args === "function" ? args(...params) : Object.keys(args).every((key) => {
                return key in params[0] && params[0][key] === args[key];
              });
              if (argsOk) {
                resolve(...params);
                if (options.resetLog) {
                  me.clearLog(eventName);
                }
                detacher2();
              }
            },
            expires: options.expires,
            prio: -1e4
            // Let others do their stuff first
          });
        } else {
          me.on({
            [eventName]: (...params) => {
              resolve(...params);
              if (options.resetLog) {
                me.clearLog(eventName);
              }
            },
            expires: options.expires,
            prio: -1e4,
            // Let others do their stuff first
            once: true
            // promises can only be resolved once anyway
          });
        }
      });
    }
    clearLog(eventName) {
      if (this._triggered) {
        if (eventName) {
          delete this._triggered[eventName];
        } else {
          this._triggered = {};
        }
      }
    }
    //endregion
    //region Trigger
    /**
     * Triggers an event, calling all registered listeners with the supplied arguments. Returning false from any listener
     * makes function return false.
     * @param {String} eventName Event name for which to trigger listeners
     * @param {Object} [param] Single parameter passed on to listeners, source property will be added to it (this)
     * @param {Boolean} [param.bubbles] Pass as `true` to indicate that the event will bubble up the widget
     * ownership hierarchy. For example up a `Menu`->`parent` Menu tree, or a `Field`->`Container` tree.
     * @typings param -> {{bubbles?: boolean, [key: string]: any}}
     * @returns {Boolean|Promise} Returns false if any listener returned `false`, or a `Promise` yielding
     * `true` / `false` based on what is returned from the async listener functions, otherwise `true`
     * @async
     * @advanced
     */
    trigger(eventName, param) {
      var _a6, _b2, _c2, _d;
      const me = this, name = eventName.toLowerCase(), {
        eventsSuspended,
        relayAllTargets,
        callOnFunctions
      } = me;
      let listeners = (_a6 = me.eventListeners) == null ? void 0 : _a6[name], handlerPromises;
      if (!me._triggered) {
        me._triggered = {};
      }
      me._triggered[eventName] = true;
      if (eventsSuspended) {
        if (eventsSuspended.shouldQueue) {
          eventsSuspended.queue.push(arguments);
        }
        return true;
      }
      if ((_b2 = me.eventListeners) == null ? void 0 : _b2.catchall) {
        (listeners = listeners ? listeners.slice() : []).push(...me.eventListeners.catchall);
        listeners.sort(priorityComparator);
      }
      if (!listeners && !relayAllTargets && !callOnFunctions) {
        return true;
      }
      if (param) {
        if (!("source" in param)) {
          if (Object.isExtensible(param)) {
            param.source = me;
          } else {
            param = Object.create(param);
            Object.assign(param, {
              source: me
            });
          }
        }
      } else {
        param = {
          source: me
        };
      }
      if (param.type !== name) {
        if (param.constructor !== Object) {
          Reflect.defineProperty(param, "type", { get: () => name });
        } else {
          param.type = name;
        }
      }
      param.eventName = eventName;
      if (!("bubbles" in param) && ((_c2 = me.bubbleEvents) == null ? void 0 : _c2[eventName])) {
        param.bubbles = me.bubbleEvents[eventName];
      }
      if (callOnFunctions) {
        const fnName = "on" + StringHelper.capitalize(eventName);
        if (fnName in me) {
          const result = me[fnName] ? me.callback(me[fnName], me, [param]) : true;
          let inhibit;
          if (Objects.isPromise(result)) {
            (handlerPromises || (handlerPromises = [])).push(result);
          } else {
            inhibit = result === false || inhibit;
          }
          if (!me.isDestroyed && hasOwnProperty5.call(me, fnName) && !((_d = me.pluginFunctionChain) == null ? void 0 : _d[fnName])) {
            const myProto2 = Object.getPrototypeOf(me);
            if (fnName in myProto2) {
              const result2 = myProto2[fnName].call(me, param);
              if (Objects.isPromise(result2)) {
                (handlerPromises || (handlerPromises = [])).push(result2);
              } else {
                inhibit = result2 === false || inhibit;
              }
              if (me.isDestroyed) {
                return;
              }
            }
          }
          if (inhibit) {
            return false;
          }
        }
      }
      let ret;
      if (listeners) {
        let i = 0, internalAbort = false;
        listeners.$firing = true;
        for (i; i < listeners.length && !me.isDestroyed && !internalAbort; i++) {
          const listener = listeners[i];
          if (ret === false && !listener.$internal) {
            continue;
          }
          let handler, thisObj = listener.thisObj;
          if (!thisObj || !thisObj.isDestroyed) {
            listener.called = true;
            if (listener.once) {
              me.removeListener(name, listener);
            }
            if (typeof listener.fn === "string") {
              if (thisObj) {
                handler = thisObj[listener.fn];
              }
              if (!handler) {
                const result2 = me.resolveCallback(listener.fn);
                handler = result2.handler;
                thisObj = result2.thisObj;
              }
            } else {
              handler = listener.fn;
            }
            if (listener.buffer) {
              if (!listener.bufferFn) {
                const buffer = Number(listener.buffer);
                if (typeof buffer !== "number" || isNaN(buffer)) {
                  throw new Error(`Incorrect type for buffer, got "${buffer}" (expected a Number)`);
                }
                listener.bufferFn = FunctionHelper.createBuffered(handler, buffer, thisObj, listener.args);
              }
              handler = listener.bufferFn;
            }
            if (listener.throttle) {
              const throttle = Number(listener.throttle);
              if (typeof throttle !== "number" || isNaN(throttle)) {
                throw new Error(`Incorrect type for throttle, got "${throttle}" (expected a Number)`);
              }
              if (!listener.throttledFn) {
                listener.throttledFn = FunctionHelper.createThrottled(handler, throttle, thisObj, listener.args);
              }
              handler = listener.throttledFn;
            }
            const result = handler.call(thisObj || me, ...listener.args || [], param);
            if (ret !== false) {
              ret = result;
            }
            if (listener.$internal && result === false) {
              internalAbort = true;
            }
            if (Objects.isPromise(result)) {
              result.$internal = listener.$internal;
              (handlerPromises || (handlerPromises = [])).push(result);
            }
          }
        }
        listeners.$firing = false;
        if (internalAbort) {
          return false;
        }
      }
      relayAllTargets == null ? void 0 : relayAllTargets.forEach((config) => {
        let name2 = eventName;
        if (config.transformCase) {
          name2 = StringHelper.capitalize(name2);
        }
        if (config.prefix) {
          name2 = config.prefix + name2;
        }
        if (config.through.trigger(name2, param) === false) {
          return false;
        }
      });
      if (param.bubbles && me.owner && !me.owner.isDestroyed) {
        return me.owner.trigger(eventName, param);
      }
      handlerPromises = handlerPromises == null ? void 0 : handlerPromises.filter((p) => ret !== false || p.$internal);
      if (handlerPromises == null ? void 0 : handlerPromises.length) {
        return new Promise((resolve) => {
          Promise.allSettled(handlerPromises).then((results) => {
            const finalResult = !results.some((result) => {
              return result.status === "fulfilled" && result.value === false;
            });
            resolve(finalResult);
          });
        });
      }
      return ret !== false;
    }
    /**
     * Prevents events from being triggered until {@link #function-resumeEvents()} is called. Optionally queues events that are triggered while
     * suspended. Multiple calls stack to require matching calls to `resumeEvents()` before actually resuming.
     * @param {Boolean} queue Specify true to queue events triggered while suspended
     * @advanced
     */
    suspendEvents(queue = false) {
      const eventsSuspended = this.eventsSuspended || (this.eventsSuspended = { shouldQueue: queue, queue: [], count: 0 });
      eventsSuspended.count++;
    }
    /**
     * Resume event triggering after a call to {@link #function-suspendEvents()}. If any triggered events were queued they will be triggered.
     * @returns {Boolean} `true` if events have been resumed (multiple calls to suspend require an equal number of resume calls to resume).
     * @advanced
     */
    resumeEvents() {
      const suspended = this.eventsSuspended;
      if (suspended) {
        if (--suspended.count === 0) {
          this.eventsSuspended = null;
          if (suspended.shouldQueue) {
            for (const queued of suspended.queue) {
              this.trigger(...queued);
            }
          }
        }
      }
      return !Boolean(this.eventsSuspended);
    }
    //endregion
  }, __publicField(_a5, "$name", "Events"), _a5;
};

// ../Core/lib/Core/helper/AsyncHelper.js
var AsyncHelper = class {
  /**
   * Returns a promise that resolves on next animation frame.
   * ```
   *  async method() {
   *      // do work
   *      await AsyncHelper.animationFrame();
   *      // do more work
   *  }
   * ```
   * @async
   */
  static animationFrame() {
    return new Promise((resolve) => {
      requestAnimationFrame(resolve);
    });
  }
  /**
   * Returns a promise that resolves after a specified number of milliseconds.
   * ```
   *  async method() {
   *      await AsyncHelper.sleep(10);
   *      // ...
   *  }
   * ```
   * @param {Number} millis The number of milliseconds to sleep.
   * @async
   */
  static sleep(millis) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, millis);
    });
  }
  /**
   * Returns a Promise which resolves when the passed function returns a truthy value.
   * ```
   *  async method() {
   *       / Wait for a maximum of ten seconds for the eventList to be empty
   *      await AsyncHelper.waitFor(this.eventList.length === 0, 10000);
   *      // ...
   *  }
   * ```
   * @param {Function} fn The function to call repeatedly until it returns a truthy value.
   * @param {Number} [maxWait=60000] The maximum time in milliseconds to wait for the function to return a truthy value.
   * pass `null` to wait indefinitely, pass as a negative number to resolve instead of reject if the wait fails.
   * @param {Number} [interval=100] The interval in milliseconds between calls to the function.
   * @returns {Promise} A Promise which yields the value returned by the passed function *if* it is truthy.
   * @async
   * @internal
   */
  static waitFor(fn2, maxWait = 6e4, interval = 100) {
    let resolveFn;
    const result = new Promise((resolve, reject) => {
      resolveFn = resolve;
      let timer;
      const predicate = () => {
        const result2 = fn2();
        if (result2) {
          clearInterval(timer);
          resolve(result2);
          return true;
        }
      };
      if (!predicate()) {
        timer = setInterval(predicate, interval);
        if (maxWait) {
          setTimeout(() => {
            clearInterval(timer);
            maxWait > 0 ? reject() : resolve();
          }, Math.abs(maxWait));
        }
      }
    });
    result.resolve = resolveFn;
    return result;
  }
  /**
   * Returns a promise that resolves as soon as possible, allowing the browser to minimally process other messages.
   * This is the shortest possible delay the browser offers, so be aware that it does not necessarily allow the
   * browser to paint or reflow if used in a long loop. It does, however, allow other async methods to execute.
   * ```
   *  async method() {
   *      await AsyncHelper.yield();
   *      // ...
   *  }
   * ```
   * @async
   */
  static yield() {
    return Promise.resolve();
  }
  /**
   * Returns a promise that resolves when all finite animations on the element have finished.
   * @param {Element} element Target element to check for animations
   * @param {Boolean} [subtree] Include animations from descendants of the element
   * @async
   * @internal
   */
  static async waitForAnimations(element, subtree = true) {
    const allAnimations = element.getAnimations({ subtree }), finiteAnimations = allAnimations.flatMap((a) => a.effect.getTiming().iterations !== Infinity ? a.finished : []);
    if (finiteAnimations.length) {
      await Promise.all(finiteAnimations);
    }
  }
};
AsyncHelper._$name = "AsyncHelper";

// ../Core/lib/Core/helper/util/Rectangle.js
var zeroBased;
var allBorders = ["border-top-width", "border-right-width", "border-bottom-width", "border-left-width"];
var allMargins = ["margin-top", "margin-right", "margin-bottom", "margin-left"];
var allPaddings = ["padding-top", "padding-right", "padding-bottom", "padding-left"];
var borderNames = {
  t: "border-top-width",
  r: "border-right-width",
  b: "border-bottom-width",
  l: "border-left-width"
};
var paddingNames = {
  t: "padding-top",
  r: "padding-right",
  b: "padding-bottom",
  l: "padding-left"
};
var alignSpecRe = /^([trblc])(\d*)-([trblc])(\d*)$/i;
var alignPointRe = /^([trblc])(\d*)$/i;
var edgeNames = [
  "top",
  "right",
  "bottom",
  "left"
];
var edgeIndices = {
  t: 0,
  r: 1,
  b: 2,
  l: 3
};
var defaultAlignments = [
  "b-t",
  "l-r",
  "t-b",
  "r-l"
];
var edgeAligments = {
  bt: 1,
  tb: 1,
  lr: 2,
  rl: 2
};
var zeroOffsets = Object.freeze([0, 0]);
var matchDimensions = ["width", "height"];
var parseEdges = (top, right = top, bottom = top, left = right) => (
  // use apply() to spread array and apply default values to missing elements
  Array.isArray(top) ? parseEdges.apply(null, top) : [top, right, bottom, left]
);
var parseTRBL = (top, right, bottom, left) => Array.isArray(top) ? parseEdges.apply(null, top) : typeof top.top === "number" ? [top.top, top.right, top.bottom, top.left] : [top, right, bottom, left];
var parseAlign = (alignSpec, rtl) => {
  const parts = alignSpecRe.exec(alignSpec), myEdge = parts[1], targetEdge = parts[3], mO = parseInt(parts[2] || 50), tO = parseInt(parts[4] || 50), myOffset = rtl && !(edgeIndices[myEdge] & 1) ? 100 - mO : mO, targetOffset = rtl && !(edgeIndices[targetEdge] & 1) ? 100 - tO : tO, edgeAligned = edgeAligments[myEdge + targetEdge];
  return {
    myAlignmentPoint: myEdge + myOffset,
    // l0
    myEdge,
    // l
    myOffset,
    // 0
    targetAlignmentPoint: targetEdge + targetOffset,
    // r0
    targetEdge,
    // r
    targetOffset,
    // 0
    startZone: edgeIndices[targetEdge],
    // 1 - start trying zone 1 in TRBL order
    edgeAligned
    // Edge-to-edge align requested
  };
};
var flipAlign = (align) => `${edgeNames[(edgeIndices[align.myEdge] + 2) % 4][0]}${align.myOffset}-${edgeNames[(edgeIndices[align.targetEdge] + 2) % 4][0]}${align.targetOffset}`;
var createOffsets = (offset) => {
  if (offset == null) {
    return zeroOffsets;
  } else if (typeof offset === "number") {
    return [offset, offset];
  }
  return offset;
};
var hiddenOverflow = {
  hidden: 1,
  clip: 1
};
var Rectangle = class _Rectangle {
  /**
   * Constructs a Rectangle
   * @param {Number} x The X coordinate
   * @param {Number} y The Y coordinate
   * @param {Number} width The width
   * @param {Number} height The height
   */
  constructor(x, y, width, height, assert = true) {
    // Class does not extend Base, so we need to define this
    __publicField(this, "isRectangle", true);
    if (assert) {
      ObjectHelper.assertNumber(x, "Rectangle.x");
      ObjectHelper.assertNumber(y, "Rectangle.y");
      ObjectHelper.assertNumber(width, "Rectangle.width", true);
      ObjectHelper.assertNumber(height, "Rectangle.height", true);
    }
    const me = this;
    if (width < 0) {
      x += width;
      width = -width;
    }
    if (height < 0) {
      y += height;
      height = -height;
    }
    me._x = x;
    me._y = y;
    me._width = width;
    me._height = height;
  }
  /**
   * Returns the Rectangle in document based coordinates of the passed element.
   *
   * *Note:* If the element passed is the `document` or `window` the `window`'s
   * rectangle is returned which is always at `[0, 0]` and encompasses the
   * browser's entire document viewport.
   * @param {HTMLElement|Core.widget.Widget|Core.widget.Mask|String} element The element (or selector) to calculate the Rectangle for.
   * @param {HTMLElement|String} [relativeTo] Optionally, a parent element (or selector) in whose space to calculate the Rectangle.
   * @param {Boolean} [ignorePageScroll=false] Use browser viewport based coordinates.
   * @returns {Core.helper.util.Rectangle} The Rectangle in document based (or, optionally viewport based) coordinates. Relative to the _relativeTo_ parameter if passed.
   */
  static from(element, relativeTo, ignorePageScroll) {
    if (typeof element === "string") {
      element = document.querySelector(element);
    } else if ((element == null ? void 0 : element.nodeType) === Node.DOCUMENT_FRAGMENT_NODE) {
      element = element.host || element.ownerDocument;
    }
    if (typeof relativeTo === "string") {
      relativeTo = document.querySelector(relativeTo);
    }
    if (element == null || element.isRectangle) {
      return element;
    }
    element = element.element || element;
    if (ignorePageScroll === void 0 && typeof relativeTo === "boolean") {
      ignorePageScroll = relativeTo;
      relativeTo = null;
    }
    if (!(relativeTo == null ? void 0 : relativeTo.isRectangle)) {
      if (relativeTo) {
        let { scrollLeft, scrollTop } = relativeTo;
        if (BrowserHelper.isSafari && relativeTo === document.body) {
          scrollLeft = scrollTop = 0;
        }
        relativeTo = _Rectangle.from(relativeTo).translate(-scrollLeft, -scrollTop);
      } else {
        if (!zeroBased) {
          zeroBased = new _Rectangle(0, 0, 0, 0);
        }
        relativeTo = zeroBased;
      }
    }
    const isViewport = element === document || element === globalThis, isSFViewport = element === document.body && document.body.offsetHeight === 0, sfElRect = isSFViewport && element.getBoundingClientRect(), viewRect = isSFViewport ? new _Rectangle(sfElRect.left, sfElRect.top, sfElRect.width, document.body.parentElement.scrollHeight) : isViewport ? new _Rectangle(0, 0, globalThis.innerWidth, globalThis.innerHeight) : element.getBoundingClientRect(), scrollOffset = ignorePageScroll || isViewport ? [0, 0] : [globalThis.pageXOffset, globalThis.pageYOffset];
    return new _Rectangle(
      viewRect.left + scrollOffset[0] - relativeTo.x,
      viewRect.top + scrollOffset[1] - relativeTo.y,
      viewRect.width,
      viewRect.height
    );
  }
  /**
   * Returns the Rectangle in viewport coordinates of the passed element.
   *
   * *Note:* If the element passed is the `document` or `window` the `window`'s rectangle is returned which is always
   * at `[0, 0]` and encompasses the browser's entire document viewport.
   * @param {HTMLElement} element The element to calculate the Rectangle for.
   * @param {HTMLElement} [relativeTo] Optionally, a parent element in whose space to calculate the Rectangle.
   * @returns {Core.helper.util.Rectangle} The Rectangle in viewport based coordinates. Relative to the _relativeTo_
   * parameter if provided.
   */
  static fromScreen(element, relativeTo) {
    return _Rectangle.from(
      element,
      relativeTo,
      /* ignorePageScroll = */
      true
    );
  }
  /**
   * Returns the inner Rectangle (within border) in document based coordinates of the passed element.
   * @param {HTMLElement} element The element to calculate the Rectangle for.
   * @param {HTMLElement} [relativeTo] Optionally, a parent element in whose space to calculate the Rectangle.
   * @param {Boolean} [ignorePageScroll] Use browser viewport based coordinates.
   * @returns {Core.helper.util.Rectangle} The Rectangle in document based (or, optionally viewport based) coordinates. Relative to the _relativeTo_ parameter if passed.
   */
  static inner(element, relativeTo, ignorePageScroll = false) {
    const result = this.from(element, relativeTo, ignorePageScroll);
    if (document.body.contains(element)) {
      const borders = DomHelper.getStyleValue(element, allBorders);
      result.x += parseFloat(borders[borderNames.l]);
      result.y += parseFloat(borders[borderNames.t]);
      result.right -= parseFloat(borders[borderNames.r]);
      result.bottom -= parseFloat(borders[borderNames.b]);
    }
    return result;
  }
  /**
   * Returns the content Rectangle (within border and padding) in document based coordinates of the passed element.
   * @param {HTMLElement} element The element to calculate the Rectangle for.
   * @param {HTMLElement} [relativeTo] Optionally, a parent element in whose space to calculate the Rectangle.
   * @param {Boolean} [ignorePageScroll] Use browser viewport based coordinates.
   * @returns {Core.helper.util.Rectangle} The Rectangle in document based (or, optionally viewport based) coordinates. Relative to the _relativeTo_ parameter if passed.
   */
  static content(element, relativeTo, ignorePageScroll = false) {
    const result = this.from(element, relativeTo, ignorePageScroll);
    if (document.body.contains(element)) {
      const borders = DomHelper.getStyleValue(element, allBorders), padding = DomHelper.getStyleValue(element, allPaddings);
      result.x += parseFloat(borders[borderNames.l]) + parseFloat(padding[paddingNames.l]);
      result.y += parseFloat(borders[borderNames.t]) + parseFloat(padding[paddingNames.t]);
      result.right -= parseFloat(borders[borderNames.r]) + parseFloat(padding[paddingNames.r]);
      result.bottom -= parseFloat(borders[borderNames.b]) + parseFloat(padding[paddingNames.b]);
    }
    return result;
  }
  /**
   * Returns the client Rectangle (within border and padding and scrollbars) in document based coordinates of the
   * passed element.
   * @param {HTMLElement} element The element to calculate the Rectangle for.
   * @param {HTMLElement} [relativeTo] Optionally, a parent element in whose space to calculate the Rectangle.
   * @param {Boolean} [ignorePageScroll] Use browser viewport based coordinates.
   * @param {Boolean} [clipStickies] Return the Rectangle *within* any sticky elements docked at the element edges.
   * @returns {Core.helper.util.Rectangle} The Rectangle in document based (or, optionally viewport based) coordinates. Relative to the _relativeTo_ parameter if passed.
   */
  static client(element, relativeTo, ignorePageScroll = false, clipStickies, targetEl, includePadding = true) {
    const result = this[includePadding ? "content" : "inner"](element, relativeTo, ignorePageScroll), scrollbarWidth = DomHelper.scrollBarWidth;
    let padding;
    if (scrollbarWidth && !element.classList.contains("b-hide-scroll")) {
      if (element.scrollHeight > element.clientHeight && !hiddenOverflow[DomHelper.getStyleValue(element, "overflow-y")]) {
        padding = includePadding ? parseFloat(DomHelper.getStyleValue(element, "padding-inline-end")) : 0;
        if (DomHelper.getStyleValue(element, "direction") === "rtl") {
          result.x += padding + Math.max(padding, scrollbarWidth);
        } else {
          result.right += padding - Math.max(padding, scrollbarWidth);
        }
      }
      if (element.scrollWidth > element.clientWidth && !hiddenOverflow[DomHelper.getStyleValue(element, "overflow-x")]) {
        padding = includePadding ? parseFloat(DomHelper.getStyleValue(element, "padding-bottom")) : 0;
        result.bottom += padding - Math.max(padding, scrollbarWidth);
      }
    }
    if (clipStickies) {
      this.clipStickies(result, element, relativeTo, ignorePageScroll = false, targetEl);
    }
    return result;
  }
  static clipStickies(rect, element, relativeTo, ignorePageScroll = false, targetEl) {
    let stickies = [...element.children].filter((e) => DomHelper.getStyleValue(e, "position") === "sticky");
    if (targetEl) {
      const targetRect = targetEl && _Rectangle.from(targetEl, relativeTo, ignorePageScroll);
      stickies = stickies.filter((e) => !e.contains(targetEl) && targetRect.intersect(_Rectangle.from(e)));
    }
    for (let i = 0, { length } = stickies; i < length; i++) {
      const e = stickies[i], r = this.fromScreen(e);
      if (parseFloat(DomHelper.getStyleValue(e, "left")) === 0) {
        rect.x += r.width;
      } else if (parseFloat(DomHelper.getStyleValue(e, "right")) === 0) {
        rect.right -= r.width;
      } else if (parseFloat(DomHelper.getStyleValue(e, "top")) === 0) {
        rect.y += r.height;
      } else if (parseFloat(DomHelper.getStyleValue(e, "bottom")) === 0) {
        rect.bottom -= r.height;
      }
    }
    return rect;
  }
  /**
   * Returns the outer Rectangle (including margin) in document based coordinates of the passed element.
   * @param {HTMLElement} element The element to calculate the Rectangle for.
   * @param {HTMLElement} [relativeTo] Optionally, a parent element in whose space to calculate the Rectangle.
   * @param {Boolean} [ignorePageScroll] Use browser viewport based coordinates.
   * @returns {Core.helper.util.Rectangle} The Rectangle in document based (or, optionally viewport based) coordinates.
   * Relative to the _relativeTo_ parameter if passed.
   * @internal
   */
  static outer(element, relativeTo, ignorePageScroll = false) {
    const result = this.from(element, relativeTo, ignorePageScroll);
    if (document.body.contains(element)) {
      const margin = DomHelper.getStyleValue(element, allMargins);
      result.x -= parseFloat(margin["margin-left"]);
      result.y -= parseFloat(margin["margin-top"]);
      result.right += parseFloat(margin["margin-right"]);
      result.bottom += parseFloat(margin["margin-bottom"]);
    }
    return result;
  }
  /**
   * Returns a new rectangle created as the union of all supplied rectangles.
   * @param {...Core.helper.util.Rectangle} rectangles
   * @returns {Core.helper.util.Rectangle}
   */
  static union(...rectangles) {
    let { x, y, right, bottom } = rectangles[0], current;
    if (rectangles.length > 1) {
      for (let i = 1; i < rectangles.length; i++) {
        current = rectangles[i];
        if (current.x < x) {
          x = current.x;
        }
        if (current.y < y) {
          y = current.y;
        }
        if (current.right > right) {
          right = current.right;
        }
        if (current.bottom > bottom) {
          bottom = current.bottom;
        }
      }
    }
    return new _Rectangle(x, y, right - x, bottom - y);
  }
  /**
   * Rounds this Rectangle to the pixel resolution of the current display or to the nearest
   * passed unit which defaults to the current display's [`devicePixelRatio`](https://developer.mozilla.org/en-US/docs/Web/API/Window/devicePixelRatio).
   * @param {Number} [devicePixelRatio] device pixel ratio which defaults to `window.devicePixelRatio`
   */
  roundPx(devicePixelRatio = globalThis.devicePixelRatio || 1) {
    const me = this;
    me._x = DomHelper.roundPx(me._x, devicePixelRatio);
    me._y = DomHelper.roundPx(me._y, devicePixelRatio);
    me._width = DomHelper.roundPx(me._width, devicePixelRatio);
    me._height = DomHelper.roundPx(me._height, devicePixelRatio);
    return me;
  }
  // This class doesn't extend Base and extending doesn't seem to be
  // the way to go. Instead we duplicate smallest piece of logic here
  static get $$name() {
    return hasOwnProperty.call(this, "$name") && this.$name || // _$name is filled by webpack for every class (cls._$name = '...')
    hasOwnProperty.call(this, "_$name") && this._$name || this.name;
  }
  get $$name() {
    return this.constructor.$$name;
  }
  /**
   * Creates a copy of this Rectangle.
   */
  clone() {
    const me = this, result = new _Rectangle(me.x, me.y, me.width, me.height, false);
    result.isAlignRectangle = me.isAlignRectangle;
    result.minHeight = me.minHeight;
    result.minWidth = me.minWidth;
    return result;
  }
  /**
   * Returns `true` if this Rectangle wholly contains the passed rectangle.
   *
   * Note that a {@link Core.helper.util.Rectangle.Point} may be passed.
   * @param {Core.helper.util.Rectangle} other The Rectangle to test for containment within this Rectangle
   * @returns {Boolean} `true` if the other Rectangle is wholly contained within this Rectangle
   */
  contains(other) {
    const me = this;
    if (other.isRectangle) {
      return other._x >= me._x && other._y >= me._y && other.right <= me.right && other.bottom <= me.bottom;
    } else {
      return false;
    }
  }
  /**
   * Clips off the area of the "other" Rectangle(s) from this Rectangle if any of the the other rectangles occlude
   * a whole edge of this Rectangle.
   *
   * In the example below, the `other` Rectangle obscures the whole of the left edge of `this` Rectangle.
   *
   * The result from this call would be a Rectangle with the left edge moved to the right edge of the `other` Rectangle.
   *
   * This is useful for clipping off sticky elements which are docked to the edges of an element.
   *
   * ```
   *   other                   this
   * ┌───────┐─────────────────────────────────────────┐
   * │   │   │                                         │
   * │   │   │                                         │
   * │   │   │                                         │
   * │   │   │                                         │
   * │   │   │                                         │
   * │   │   │                                         │
   * └───────┘─────────────────────────────────────────┘
   * ```
   * @param {...Core.helper.util.Rectangle} other The potentially occluding Rectangle(s).
   * @returns {Core.helper.util.Rectangle} A *new* rectangle with the area of the `other` Rectangle clipped off.
   */
  exclude(...args) {
    var _a5, _b2;
    const me = this;
    let other, result = me.clone();
    for (other of Array.from(args).flat()) {
      let atStart, intersection, inflateArgs = [0, 0, 0, 0];
      if (((atStart = other.x <= me.x) || other.right >= me.right) && ((_a5 = intersection = other.intersect(me)) == null ? void 0 : _a5.height) >= me.height) {
        inflateArgs[atStart ? 3 : 1] = -intersection.width;
      } else if (((atStart = other.y <= me.y) || other.bottom >= me.bottom) && ((_b2 = intersection = other.intersect(me)) == null ? void 0 : _b2.width) >= me.width) {
        inflateArgs[atStart ? 0 : 2] = -intersection.height;
      }
      result = result.inflate(...inflateArgs);
    }
    return result;
  }
  /**
   * Checks if this Rectangle intersects the passed Rectangle
   * @param {Core.helper.util.Rectangle} other The Rectangle to intersect with this.
   * @param {Boolean} [useBoolean] Specify `true` to return a boolean value instead of constructing a new Rectangle
   * @param {Boolean} [allowZeroDimensions] `true` to consider zero-width or zero-hight rectangles as intersecting if coordinates indicate the intersection
   * @returns {Core.helper.util.Rectangle|Boolean} Returns the intersection Rectangle or `false` if there is no intersection.
   */
  intersect(other, useBoolean = false, allowZeroDimensions = false) {
    const me = this, y = Math.max(me.y, other.y), r = Math.min(me.right, other.right), b = Math.min(me.bottom, other.bottom), x = Math.max(me.x, other.x), intersect = allowZeroDimensions ? b >= y && r >= x : b > y && r > x;
    if (intersect) {
      return useBoolean ? true : new _Rectangle(x, y, r - x, b - y);
    } else {
      return false;
    }
  }
  equals(other, round2 = false) {
    const processor = round2 ? (x) => Math.round(x) : (x) => x;
    return other.isRectangle && processor(other.x) === processor(this.x) && processor(other.y) === processor(this.y) && processor(other.width) === processor(this.width) && processor(other.height) === processor(this.height);
  }
  /**
   * Translates this Rectangle by the passed vector. Size is maintained.
   * @param {Number} x The X translation vector.
   * @param {Number} y The Y translation vector.
   * @returns {Core.helper.util.Rectangle} This Rectangle;
   */
  translate(x, y) {
    this._x += x || 0;
    this._y += y || 0;
    return this;
  }
  /**
   * Moves this Rectangle to the passed `x`, `y` position. Size is maintained.
   * @param {Number} x The new X position.
   * @param {Number} y The new Y position.
   * @returns {Core.helper.util.Rectangle}  This Rectangle;
   */
  moveTo(x, y) {
    if (x != null) {
      this._x = x;
    }
    if (y != null) {
      this._y = y;
    }
    return this;
  }
  /**
   * Returns the vector which would translate this Rectangle (or Point) to the same position as the other Rectangle (or point)
   * @param {Core.helper.util.Rectangle|Core.helper.util.Rectangle.Point} other The Rectangle or Point to calculate the delta to.
   * @returns {Array} Returns a vector using format `[deltaX, deltaY]`
   * @internal
   */
  getDelta(other) {
    return [other.x - this.x, other.y - this.y];
  }
  /**
   * The center point of this rectangle.
   * @property {Core.helper.util.Rectangle.Point}
   */
  get center() {
    const result = new Point(this.x + this.width / 2, this.y + this.height / 2, 0, 0);
    result.target = this.target;
    return result;
  }
  /**
   * Get/sets the X coordinate of the Rectangle. Note that this does *not* translate the
   * Rectangle. The requested {@link #property-width} will change.
   * @property {Number}
   */
  set x(x) {
    const xDelta = x - this._x;
    this._x = x;
    this._width -= xDelta;
  }
  get x() {
    return this._x;
  }
  get start() {
    return this.left;
  }
  /**
   * Alias for x. To match DOMRect.
   * @property {Number}
   */
  set left(x) {
    this.x = x;
  }
  get left() {
    return this.x;
  }
  /**
   * Alias for y. To match DOMRect.
   * @property {Number}
   */
  set top(y) {
    this.y = y;
  }
  get top() {
    return this.y;
  }
  /**
   * Get/sets the Y coordinate of the Rectangle. Note that this does *not* translate the
   * Rectangle. The requested {@link #property-height} will change.
   * @property {Number}
   */
  set y(y) {
    const yDelta = y - this._y;
    this._y = y;
    this._height -= yDelta;
  }
  get y() {
    return this._y;
  }
  /**
   * Get/sets the width of the Rectangle. Note that the requested {@link #property-right} will change.
   * @property {Number}
   */
  set width(width) {
    ObjectHelper.assertNumber(width, "Rectangle.width", !this._allowNegative);
    this._width = width;
  }
  get width() {
    return this._width;
  }
  /**
   * Get/sets the height of the Rectangle. Note that the requested {@link #property-bottom} will change.
   * @property {Number}
   */
  set height(height) {
    ObjectHelper.assertNumber(height, "Rectangle.height", !this._allowNegative);
    this._height = height;
  }
  get height() {
    return this._height;
  }
  /**
   * Get/sets the right edge of the Rectangle. Note that the requested {@link #property-width} will change.
   *
   * The right edge value is exclusive of the calculated rectangle width. So x=0 and right=10
   * means a width of 10.
   * @property {Number}
   */
  set right(right) {
    this._width = right - this._x;
  }
  get right() {
    return this._x + this._width;
  }
  get end() {
    return this.right;
  }
  /**
   * Get/sets the bottom edge of the Rectangle. Note that the requested {@link #property-height} will change.
   *
   * The bottom edge value is exclusive of the calculated rectangle height. So y=0 and bottom=10
   * means a height of 10.
   * @property {Number}
   */
  set bottom(bottom) {
    this._height = bottom - this._y;
  }
  get bottom() {
    return this._y + this._height;
  }
  getStart(rtl, horizontal = true) {
    if (horizontal) {
      return rtl ? this.right : this.left;
    }
    return this.top;
  }
  getEnd(rtl, horizontal = true) {
    if (horizontal) {
      return rtl ? this.left : this.right;
    }
    return this.bottom;
  }
  get area() {
    return this.width * this.height;
  }
  set minWidth(minWidth) {
    const me = this;
    if (isNaN(minWidth)) {
      me._minWidth = null;
    } else {
      me._minWidth = Number(minWidth);
      if (!me.isAlignRectangle) {
        me.width = Math.max(me.width, me._minWidth);
      }
    }
  }
  get minWidth() {
    return this._minWidth;
  }
  set minHeight(minHeight) {
    const me = this;
    if (isNaN(minHeight)) {
      me._minHeight = null;
    } else {
      me._minHeight = Number(minHeight);
      if (!me.isAlignRectangle) {
        me.height = Math.max(me.height, me._minHeight);
      }
    }
  }
  get minHeight() {
    return this._minHeight;
  }
  /**
   * Modifies the bounds of this Rectangle by the specified deltas.
   * @param {Number} x How much to *add* to the x position.
   * @param {Number} y  How much to *add* to the y position.
   * @param {Number} width  How much to add to the width.
   * @param {Number} height  How much to add to the height.
   * @returns {Core.helper.util.Rectangle} This Rectangle
   */
  adjust(x, y, width, height, allowNegative) {
    const me = this;
    me._allowNegative = allowNegative;
    me.x += x;
    me.y += y;
    me.width += width;
    me.height += height;
    return me;
  }
  /**
   * Modifies the bounds of this rectangle by expanding them by the specified amount in all directions.
   * The parameters are read the same way as CSS margin values.
   *
   * Number of values passed:
   * - One: all edges are inflated by that value.
   * - Two: values are top/bottom deflation and left/right inflation.
   * - Three: values are top, left/right, and bottom.
   * - Four: the values are top, right, bottom, and left.
   *
   * @param {Number|Number[]|Core.helper.util.Rectangle|DOMRect} top How much to inflate, or the top value if more
   * than one value is passed. If an array is passed, it is spread as the arguments to this method.
   * @param {Number} [right] How much to inflate the right side, or both left and right is only two values passed. If
   * one value is passed, this defaults to the same as `top`.
   * @param {Number} [bottom] How much to inflate the bottom side. If 2 values are passed, this defaults to the `top`
   * value.
   * @param {Number} [left] How much to inflate the left side. If 3 values are passed, this defaults to the `right`
   * value.
   * @returns {Core.helper.util.Rectangle} This Rectangle
   * @internal
   */
  inflate(top, right = top, bottom = top, left = right) {
    [top, right, bottom, left] = parseTRBL(top, right, bottom, left);
    return this.adjust(-left, -top, right, bottom);
  }
  /**
   * Modifies the bounds of this rectangle by reducing them by the specified amount in all directions.
   * The parameters are read the same way as CSS margin values.
   *
   * Number of values passed:
   * - One: all edges are deflated by that value.
   * - Two: values are top/bottom deflation and left/right deflation.
   * - Three: values are top, left/right, and bottom.
   * - Four: the values are top, right, bottom, and left.
   *
   * @param {Number|Number[]|Core.helper.util.Rectangle|DOMRect} top How much to deflate, or the top value if more
   * than one value is passed. If an array is passed, it is spread as the arguments to this method.
   * @param {Number} [right] How much to deflate the right side, or both left and right is only two values passed. If
   * one value is passed, this defaults to the same as `top`.
   * @param {Number} [bottom] How much to deflate the bottom side. If 2 values are passed, this defaults to the `top`
   * value.
   * @param {Number} [left] How much to deflate the left side. If 3 values are passed, this defaults to the `right`
   * value.
   * @returns {Core.helper.util.Rectangle} This Rectangle
   * @internal
   */
  deflate(top, right = top, bottom = top, left = right) {
    [top, right, bottom, left] = parseTRBL(top, right, bottom, left);
    return this.adjust(left, top, -right, -bottom);
  }
  /**
   * Attempts to constrain this Rectangle into the passed Rectangle. If the `strict` parameter is `true`
   * then this method will return `false` if constraint could not be achieved.
   *
   * If this Rectangle has a `minHeight` or `minWidth` property, size will be adjusted while attempting to constrain.
   *
   * Right and bottom are adjusted first leaving the top and bottom sides to "win" in the case that this Rectangle overflows
   * the constrainTo Rectangle.
   * @param {Core.helper.util.Rectangle} constrainTo The Rectangle to constrain this Rectangle into if possible.
   * @param {Boolean} strict Pass `true` to return false, and leave this Rectangle unchanged if constraint
   * could not be achieved.
   * @returns {Core.helper.util.Rectangle|Boolean} This Rectangle. If `strict` is true, and constraining was not successful, `false`.
   */
  constrainTo(constrainTo, strict) {
    const me = this, originalHeight = me.height, originalY = me.y, minWidth = me.minWidth || me.width, minHeight = me.minHeight || me.height;
    if (me.height >= constrainTo.height) {
      if (strict && minHeight > constrainTo.height) {
        return false;
      }
      me._y = constrainTo.y;
      me.height = constrainTo.height;
    }
    if (me.width >= constrainTo.width) {
      if (strict && minWidth > constrainTo.width) {
        me.y = originalY;
        me.height = originalHeight;
        return false;
      }
      me._x = constrainTo.x;
      me.width = constrainTo.width;
    }
    me.translate.apply(me, me.constrainVector = [
      Math.min(constrainTo.right - me.right, 0),
      Math.min(constrainTo.bottom - me.bottom, 0)
    ]);
    me.translate(Math.max(constrainTo.x - me.x, 0), Math.max(constrainTo.y - me.y, 0));
    return me;
  }
  /**
   * Returns a cloned version of this Rectangle aligned to a target Rectangle, or element or {@link Core.widget.Widget}.
   * @param {Object} spec Alignment specification.
   * @param {HTMLElement|Core.widget.Widget|Core.helper.util.Rectangle} spec.target The Widget or element or Rectangle to align to.
   * @param {Number|Number[]} [inflate] Arguments to {@link Core.helper.util.Rectangle#function-inflate}.
   * Specify as a number to inflate the target rectangle by that many pixels, or as an array of
   * inflate values for each edge.
   * @param {Number[]} [spec.anchorSize] The `[width, height]` of the anchor pointer when in `top` position. The
   * width is the baseline length, and the height is the height of the arrow. If passed, the anchor position
   * will be calculated to be at the centre of the overlap of the two aligned edges and returned in the `anchor`
   * property of the resulting Rectangle:
   *
   *     {
   *         edge: 'top',         // or 'right' or 'bottom' or 'left'
   *         x/y: offset          // dimension to translate and value to translate by.
   *     }
   *
   * @param {Object} [spec.anchorPosition] an `{ x: n, y: n }` anchor translation to be used *if the requested alignment
   * succeeds without violating constraints*. If a fallback alignment is used, the anchor will be centered in the
   * overlap of the aligned edges as usual.
   * @param {Boolean} [spec.overlap] True to allow this to overlap the target.
   * @param {String} spec.align The edge alignment specification string, specifying two points to bring together.
   *
   * Each point is described by an edge initial (`t` for top edge, `b` for bottom edge etc) followed
   * by a percentage along that edge.
   *
   * So the form would be `[trblc][n]-[trblc][n].` The `n` is the percentage offset along that edge
   * which defines the alignment point. This is not valid for alignment point `c` which means the center point.
   *
   * For example `t0-b0' would align this Rectangle's top left corner with the bottom left corner of the `target`.
   * @param {HTMLElement|Core.widget.Widget|Core.helper.util.Rectangle} [spec.constrainTo] The Widget or Element or Rectangle to constrain to.
   * If the requested alignment cannot be constrained (it will first shrink the resulting Rectangle according
   * to the `minWidth` and `minHeight` properties of this rectangle), then it will try aligning at other edges
   * (honouring the `axisLock` option), and pick the fallback alignment which results in the shortest translation.
   * @param {Boolean} [spec.axisLock] Specify as a truthy value to fall back to aligning against the opposite
   * edge first if the requested alignment cannot be constrained into the `constrainTo` option. If specified
   * as `'flexible'`, then fallback will continue searching for solutions on the remaining two sides.
   * @param {Boolean|'min'} [spec.matchSize] When aligning edge-to-edge, match the length of the aligned-to
   * edge of the target.
   *
   * Setting this value to `'min'` means that the resulting aligned Rectangle will *grow* to the size
   * of the aligned-to edge if it is smaller, but will not *shrink* to match the aligned-to edge.
   *
   * This is only honored when `axisLock` is enabled and alignment succeeds on the requested axis.
   * If __not__ aligning edge-to-edge, `matchSize` matches both dimensions of the target.
   * @param {Number|Number[]} [spec.offset] The 'x' and 'y' offset values to create an extra margin round the target
   * to offset the aligned widget further from the target. May be configured as -ve to move the aligned widget
   * towards the target - for example producing the effect of the anchor pointer piercing the target.
   * @param {Number|Number[]} [spec.constrainPadding] The amount of pixels to pad from the `constrainTo` target,
   * either a single value, or an array of values in CSS edge order.
   * @param {Boolean} [spec.rtl] Pass as true if this is being used in an RTL environment, and aligning 0% to
   * 100% along a horizontal edge must proceed from right to left.
   * @returns {Core.helper.util.Rectangle} A new Rectangle aligned as requested if possible, but if the requested position violates
   * the `constrainTo` Rectangle, the shortest translation from the requested position which obeys constraints will be used.
   */
  alignTo(spec) {
    let result = this.clone(), {
      target,
      constrainTo,
      constrainPadding,
      inflate
    } = spec, calculatedAnchorPosition, zone, resultZone, constrainingToViewport;
    if (target) {
      target = target.isRectangle ? target.clone() : _Rectangle.from(target.element ? target.element : target);
      if (inflate) {
        target.inflate.apply(target, Array.isArray(inflate) ? inflate : [inflate]);
      }
    }
    if (constrainTo) {
      if (!constrainTo.isRectangle) {
        constrainingToViewport = constrainTo === globalThis || constrainTo === document;
        const ignorePageScroll = "ignorePageScroll" in spec ? spec.ignorePageScroll : !constrainingToViewport;
        constrainTo = _Rectangle.from(constrainTo.element ? constrainTo.element : constrainTo, null, ignorePageScroll);
      }
      if (constrainPadding) {
        constrainPadding = parseEdges(constrainPadding);
        constrainPadding[0] = Math.min(constrainPadding[0], target.top);
        constrainPadding[1] = Math.min(constrainPadding[1], constrainTo.right - target.right);
        constrainPadding[2] = Math.min(constrainPadding[2], constrainTo.bottom - target.bottom);
        constrainPadding[3] = Math.min(constrainPadding[3], target.left);
        constrainTo = constrainTo.deflate.apply(constrainTo.clone(), constrainPadding);
      }
    }
    const me = this, targetOffsets = createOffsets(spec.offset), {
      align,
      axisLock,
      anchorSize,
      anchorPosition,
      matchSize,
      position,
      rtl
    } = spec, isPoint = target && (target.width < 2 && target.height < 2), alignSpec = parseAlign(align, rtl), targetConstrainRect = constrainTo && constrainTo.clone(), constraintZones = [], zoneOrder = [{
      zone: zone = alignSpec.startZone,
      align
    }], matchDimension = matchSize && matchDimensions[alignSpec.startZone & 1], originalSize = me[matchDimension];
    if (matchDimension && axisLock) {
      result[matchDimension] = matchSize === "min" ? Math.max(result.width, target[matchDimension]) : target[matchDimension];
    } else if (!alignSpec.edgeAligned && matchSize) {
      result.width = target.width;
      result.height = target.height;
    }
    if (constrainTo) {
      result.constrainTo(constrainTo);
    }
    if (constrainTo && alignSpec.startZone != null) {
      if (axisLock) {
        zoneOrder.push({
          zone: zone = (zone + 2) % 4,
          align: flipAlign(alignSpec)
        });
        if (axisLock === "flexible") {
          zoneOrder.push({
            zone: zone = (alignSpec.startZone + 1) % 4,
            align: defaultAlignments[zone]
          });
          zoneOrder.push({
            zone: zone = (zone + 2) % 4,
            align: defaultAlignments[zone]
          });
        }
      } else {
        for (let i = 1; i < 4; i++) {
          zoneOrder.push({
            zone: zone = (zone + 1) % 4,
            align: defaultAlignments[zone]
          });
        }
      }
    }
    if (anchorPosition) {
      const pos = alignSpec.startZone & 1 ? "y" : "x";
      calculatedAnchorPosition = {
        [pos]: anchorPosition[pos],
        edge: edgeNames[(alignSpec.startZone + 2) % 4]
      };
    }
    if (targetConstrainRect && target) {
      targetConstrainRect.adjust(-target.width, -target.height, target.width, target.height);
      target.constrainTo(targetConstrainRect);
    }
    result.minWidth = me.minWidth;
    result.minHeight = me.minHeight;
    if (position) {
      result.moveTo(position.x, position.y);
      result.translate.apply(result, targetOffsets);
      if (constrainTo) {
        result.constrainTo(constrainTo);
      }
    } else {
      const centerAligned = alignSpec.myEdge === "c" || alignSpec.targetEdge === "c", offsets = anchorSize && !centerAligned ? [anchorSize[1] + targetOffsets[0], anchorSize[1] + targetOffsets[1]] : targetOffsets;
      const baseTargetPoint = target.getAlignmentPoint(alignSpec.targetAlignmentPoint), targetPoint = target.getAlignmentPoint(alignSpec.targetAlignmentPoint, offsets);
      let myPoint = result.getAlignmentPoint(alignSpec.myAlignmentPoint);
      let overlap = !isPoint && result.clone().translate(baseTargetPoint[0] - myPoint[0], baseTargetPoint[1] - myPoint[1]).intersect(target, true);
      result.translate(targetPoint[0] - myPoint[0], targetPoint[1] - myPoint[1]);
      if (overlap) {
        result.translate(-(targetPoint[0] - myPoint[0]), -(targetPoint[1] - myPoint[1]));
        myPoint = result.getAlignmentPoint(alignSpec.myAlignmentPoint);
        result.translate(baseTargetPoint[0] - myPoint[0], baseTargetPoint[1] - myPoint[1]);
        result.translate(...offsets);
        if (constrainTo) {
          result.constrainTo(constrainTo);
        }
        resultZone = alignSpec.startZone;
      } else if (constrainTo && !constrainTo.contains(result)) {
        const requestedResult = result.clone(), solutions = [];
        let zone2, largestZone;
        calculatedAnchorPosition = null;
        constraintZones[0] = zone2 = constrainTo.clone();
        zone2.bottom = target.y - offsets[1];
        constraintZones[1] = zone2 = constrainTo.clone();
        zone2.x = target.right + offsets[0];
        constraintZones[2] = zone2 = constrainTo.clone();
        zone2.y = target.bottom + offsets[1];
        constraintZones[3] = zone2 = constrainTo.clone();
        zone2.right = target.x - offsets[0];
        for (let i = 0; i < zoneOrder.length; i++) {
          if (matchDimension && i === 2) {
            result[matchDimension] = originalSize;
          }
          zone2 = constraintZones[resultZone = zoneOrder[i].zone];
          result = result.alignTo({
            target,
            offsets,
            align: zoneOrder[i].align
          });
          if (result.constrainTo(zone2, true)) {
            solutions.push({
              result,
              zone: resultZone
            });
            if (!largestZone || result.width < me.width || result.height < me.height) {
              result.align = zoneOrder[i].align;
              break;
            }
          }
          if (!largestZone || zone2.area > largestZone.area) {
            const r = result.clone();
            switch (resultZone) {
              case 0:
                r.moveTo(null, zone2.bottom - r.height);
                break;
              case 1:
                r.moveTo(zone2.left);
                break;
              case 2:
                r.moveTo(null, zone2.top);
                break;
              case 3:
                r.moveTo(zone2.right - r.width);
                break;
            }
            largestZone = {
              area: zone2.area,
              result: r,
              zone: resultZone
            };
          }
        }
        if (solutions.length) {
          if (solutions.length > 1 && !axisLock) {
            solutions.sort((s12, s22) => {
              const s1TranslationDistance = Math.sqrt((requestedResult.x - s12.result.x) ** 2 + (requestedResult.y - s12.result.y) ** 2), s2TranslationDistance = Math.sqrt((requestedResult.x - s22.result.x) ** 2 + (requestedResult.y - s22.result.y) ** 2);
              return s1TranslationDistance - s2TranslationDistance;
            });
          }
          result = solutions[0].result;
          resultZone = solutions[0].zone;
        } else {
          result = largestZone.result;
          resultZone = largestZone.zone;
          if (constrainingToViewport) {
            result.constrainTo(constrainTo);
          }
        }
      } else {
        resultZone = alignSpec.startZone;
      }
      if (isPoint) {
        result.overlap = overlap = false;
      } else {
        result.overlap = overlap = result.intersect(target, true);
      }
      result.zone = resultZone;
      if (anchorSize && !overlap) {
        if (!calculatedAnchorPosition) {
          const isLeftOrRight = resultZone & 1, start = isLeftOrRight ? "y" : "x", end = isLeftOrRight ? "bottom" : "right", startValue = Math.max(target[start], result[start]), endValue = Math.min(target[end], result[end]);
          let anchorStart = startValue + (endValue - startValue) / 2 - anchorSize[0] / 2;
          const anchorEnd = anchorStart + anchorSize[0];
          if (anchorEnd > result[end]) {
            anchorStart -= anchorEnd - result[end];
          }
          if (anchorStart < result[start]) {
            anchorStart += result[start] - anchorStart;
          }
          calculatedAnchorPosition = {
            [start]: anchorStart - result[start],
            edge: edgeNames[(resultZone + 2) % 4]
          };
        }
        result.anchor = calculatedAnchorPosition;
      }
    }
    return result;
  }
  /**
   * Returns the `[x, y]` position of the specified anchor point of this Rectangle in <edge><offset> format.
   * for example passing "t50" will return the centre point of the top edge, passing "r0" will return the start
   * position of the right edge (the top right corner).
   *
   * Note that the offset defaults to 50, so "t" means the centre of the top edge.
   * @param {String} alignmentPoint The alignment point to calculate. Must match the RegExp `[trbl]\d*`
   * @param {Number[]} margins The `[x, y]` margins to add from the left/right, top/bottom edge.
   * @internal
   */
  getAlignmentPoint(alignmentPoint, margins = zeroOffsets) {
    alignmentPoint = String(alignmentPoint);
    const me = this, parts = alignPointRe.exec(alignmentPoint) || alignSpecRe.exec(alignmentPoint), edge = parts && parts[1].toLowerCase(), edgeOffset = parts && Math.min(Math.max(parseInt(parts[2] || 50), 0), 100) / 100;
    switch (edge) {
      case "t":
        return [me.x + me.width * edgeOffset, me.y - margins[1]];
      case "r":
        return [me.right + margins[0], me.y + me.height * edgeOffset];
      case "b":
        return [me.x + me.width * edgeOffset, me.bottom + margins[1]];
      case "l":
        return [me.x - margins[0], me.y + me.height * edgeOffset];
      case "c": {
        return [me.x + me.width / 2, me.y + me.height / 2];
      }
    }
  }
  /**
   * Highlights this Rectangle using the highlighting effect of {@link Core.helper.DomHelper}
   * on a transient element which encapsulates the region's area.
   */
  highlight() {
    const me = this, highlightElement = DomHelper.createElement({
      parent: document.body,
      style: `position:absolute;z-index:9999999;pointer-events:none;
                            left:${me.x}px;top:${me.y}px;width:${me.width}px;height:${me.height}px`
    });
    return DomHelper.highlight(highlightElement).then(() => highlightElement.remove());
  }
  /**
   * Visualizes this Rectangle by adding a DOM element which encapsulates the region's area into the provided parent element.
   * @param {DomConfig} config Element config object
   * @param {Boolean} [asDomConfig] Pass `true` to return a `DomConfig` instead of a DOM element
   * @param {Boolean} [useInset] Pass `true` to position using inset instead of translate
   * @returns {Element} The highlight element
   * @internal
   */
  visualize(config, asDomConfig = false, useInset = false) {
    const me = this, domConfig = ObjectHelper.merge({
      style: {
        width: `${me.width}px`,
        height: `${me.height}px`,
        pointerEvents: "none",
        // If this visualization is provided a CSS class, let outside handle position + z-index
        ...config.class ? {} : {
          position: "absolute",
          "z-index": 9999999
        },
        ...useInset ? {
          insetInlineStart: me.x,
          top: me.y
        } : {
          left: 0,
          top: 0,
          transform: `translate(${me.x}px, ${me.y}px)`
        }
      }
    }, config);
    return asDomConfig ? domConfig : DomHelper.createElement(domConfig);
  }
  toString(delimiter = ",") {
    return [`${this.top}px`, `${this.right}px`, `${this.bottom}px`, `${this.left}px`].join(delimiter);
  }
};
var Point = class extends Rectangle {
  /**
   * Creates a new Point encapsulating the event's page position.
   * @param {Event} event The DOM event
   * @returns {Core.helper.util.Rectangle}
   * @typings ignore
   */
  static from(event, relativeToPage = true) {
    const propName = relativeToPage ? "client" : "screen";
    if (event.changedTouches) {
      event = event.changedTouches[0];
    }
    const x = event[`${propName}X`], y = event[`${propName}Y`];
    return new this(x, y);
  }
  /**
   * Constructs a Point
   * @param x The X coordinate
   * @param y The Y coordinate
   */
  constructor(x, y) {
    super(x, y, 0, 0);
  }
  /**
   * Coerces this Point to be within the passed Rectangle. Translates it into the bounds.
   * @param {Core.helper.util.Rectangle} into The Rectangle into which to coerce this Point.
   */
  constrain(into) {
    this.x = Math.min(Math.max(this.x, into.x), into.right - 1);
    this.y = Math.min(Math.max(this.y, into.y), into.bottom - 1);
    return this;
  }
  toArray() {
    return [this.x, this.y];
  }
};
Rectangle._$name = "Rectangle";

// ../Core/lib/Core/helper/util/DomClassList.js
var valueSymbol = Symbol("value");
var lengthSymbol = Symbol("length");
var DomClassList = class _DomClassList {
  static change(cls, add, remove, as = "string") {
    remove = _DomClassList.normalize(remove, "object");
    const after = _DomClassList.normalize(cls, "array").filter((c) => !remove[c]);
    if (add) {
      add = _DomClassList.normalize(add, "array");
      for (let i = 0; i < add.length; ++i) {
        if (!after.includes(add[i])) {
          after.push(add[i]);
        }
      }
    }
    return _DomClassList.normalize(after, as);
  }
  static from(classes, returnEmpty) {
    if (classes) {
      if (classes.isDomClassList) {
        returnEmpty = returnEmpty != null ? returnEmpty : true;
      } else {
        returnEmpty = returnEmpty != null ? returnEmpty : Objects.isObject(classes) && !Objects.isEmpty(classes);
        classes = new _DomClassList(classes);
      }
      if (!classes.value && !returnEmpty) {
        classes = null;
      }
    }
    return classes || (returnEmpty ? new _DomClassList() : null);
  }
  /**
   * Converts a class name of any understood type to a desired form.
   * @param {String|String[]|Object|Set|Map|HTMLElement} cls
   * @param {String} as Pass `'object'` to return an object with the class names as its keys (all keys will have a
   * value of `true`), or pass `'array'` to return an array of class names, or pass `'string'` (the default) to
   * return a space-separated string of class names.
   * @returns {String|String[]|Object}
   * @internal
   */
  static normalize(cls, as = "string") {
    cls = cls || "";
    const type = typeof cls, asArray = as === "array", asObject = as === "object", asString = !asArray && !asObject;
    let isString = type === "string", c, i, ret;
    if (type === "object") {
      if (cls.nodeType === Element.ELEMENT_NODE && typeof cls.getAttribute === "function") {
        cls = cls.getAttribute("class") || "";
        isString = true;
      } else if (cls == null ? void 0 : cls.isDomClassList) {
        cls = cls.values;
      } else if (cls instanceof DOMTokenList) {
        cls = Array.from(cls);
      } else if (cls instanceof Map) {
        cls = Array.from(cls.keys()).filter((k) => cls.get(k));
      } else if (cls instanceof Set) {
        cls = Array.from(cls);
      } else if (!Array.isArray(cls)) {
        cls = Objects.getTruthyKeys(cls);
      }
    }
    if (isString) {
      cls = StringHelper.split(cls);
    }
    for (i = cls.length; i-- > 0; ) {
      c = cls[i];
      if (!c.length) {
        cls.splice(i, 1);
      } else if (c.includes(" ")) {
        cls.splice(i, 1, ...StringHelper.split(c));
      }
    }
    cls = [...new Set(cls)];
    if (asArray) {
      ret = cls;
    } else if (asString) {
      ret = cls.join(" ");
    } else {
      ret = /* @__PURE__ */ Object.create(null);
      for (i = 0; i < cls.length; ++i) {
        ret[cls[i]] = true;
      }
    }
    return ret;
  }
  /**
   * Initializes a new DomClassList.
   * @param {...String|Object} classes The CSS classes as strings or objects.
   * @function constructor
   */
  constructor(...classes) {
    this.process(1, classes);
  }
  /**
   * Clears all class names from this DomClassList instance.
   * @returns {Core.helper.util.DomClassList} this DomClassList.
   */
  clear() {
    for (const key in this) {
      this[key] = false;
    }
    return this;
  }
  /**
   * Sets this DomClassList instance to represent the classes passed as either strings or objects.
   * @param {...String} classes The CSS classes to set.
   * @returns {Core.helper.util.DomClassList} this DomClassList.
   */
  set(...classes) {
    return this.clear().process(1, classes);
  }
  // To gain some speed in DomHelper.sync(), faster than instanceof etc
  get isDomClassList() {
    return true;
  }
  /**
   * Returns a clone of this DomClassList with all the same keys set.
   * @returns {Core.helper.util.DomClassList} A clone of this DomClassList.
   */
  clone() {
    return new _DomClassList(this);
  }
  /**
   * Returns a Boolean value, indicating whether this ClassList has the specified CSS class name.
   * @param {String} className CSS class name to check
   * @returns {Boolean} true if this ClassList contains the passed CSS class name, false otherwise
   */
  contains(className) {
    if (typeof className === "string" && className) {
      return Boolean(this[className]);
    }
    return false;
  }
  // An instance of this class may be assigned directly to an element's className
  // it will be coerced to a string value using this method.
  toString() {
    return this.length ? `${this.value} ` : "";
  }
  toJSON() {
    return this.toString();
  }
  /**
   * Analogous to string.trim, returns the string value of this `DomClassList` with no trailing space.
   * @returns {String} A concatenated string value of all the class names in this `DomClassList`
   * separated by spaces.
   */
  trim() {
    return this.value;
  }
  /**
   * Compares this DomClassList to another DomClassList (or class name string of space separated classes).
   * If the same class names (regardless of order) are present, the two are considered equal.
   *
   * So `new DomClassList('foo bar bletch').isEqual('bletch bar foo')` would return `true`
   * @param {Core.helper.util.DomClassList|String} other The `DomClassList` or string of classes to compare to.
   * @returns {Boolean} `true` if the two contain the same class names.
   */
  isEqual(other) {
    const otherClasses = _DomClassList.normalize(other, "array"), len = otherClasses.length;
    if (this.length === len) {
      for (let i = 0; i < len; i++) {
        if (!this[otherClasses[i]]) {
          return false;
        }
      }
      return true;
    }
    return false;
  }
  /**
   * Get/set string value.
   * Class names separated with space.
   * @property {String}
   */
  get value() {
    let value2 = this[valueSymbol], keys;
    if (value2 == null) {
      keys = Objects.getTruthyKeys(this);
      this[lengthSymbol] = keys.length;
      this[valueSymbol] = value2 = keys.join(" ");
    }
    return value2;
  }
  set value(value2) {
    const me = this, keys = Object.keys(me), len = keys.length;
    for (let i = 0; i < len; i++) {
      delete me[keys[i]];
    }
    if (value2) {
      me.process(1, [value2]);
    } else {
      delete me[valueSymbol];
    }
  }
  /**
   * Returns string values as an array.
   * @readonly
   * @property {String[]}
   */
  get values() {
    return Objects.getTruthyKeys(this);
  }
  get length() {
    return this.value ? this[lengthSymbol] : 0;
  }
  process(value2, classes) {
    for (let cls, k, i = 0; i < classes.length; i++) {
      if (classes[i]) {
        cls = classes[i];
        if (cls.isDomClassList || Objects.isObject(cls)) {
          for (k in cls) {
            this[k] = value2 ? cls[k] : !cls[k];
          }
        } else {
          cls = _DomClassList.normalize(classes[i], "array");
          for (k = 0; k < cls.length; ++k) {
            this[cls[k]] = value2;
          }
        }
      }
    }
    delete this[valueSymbol];
    return this;
  }
  /**
   * Adds/removes class names according to the passed object's properties.
   *
   * Properties with truthy values are added.
   * Properties with falsy values are removed.
   * @param {Object} classList Object containing properties to set/clear
   */
  assign(classList) {
    for (const cls in classList) {
      if (!this[cls] !== !classList[cls]) {
        this[cls] = classList[cls];
        delete this[valueSymbol];
      }
    }
    return this;
  }
  /**
   * Adds/removes this objects classes to the passed `classList` or element.
   *
   * Properties with truthy values are added.
   * Properties with falsy values are removed.
   * @param {HTMLElement|DOMTokenList} element The element or the element's `classList` to be updated.
   */
  assignTo(element) {
    const classList = element.nodeType === Element.ELEMENT_NODE ? element.classList : element, temp = _DomClassList.from(classList);
    temp.add(this);
    classList.value = temp.value;
  }
  /**
   * Add CSS class(es)
   * ```javascript
   * myClassList.add('bold', 'small');
   * ```
   * @param {String|String[]|Object} classes CSS classes to add
   */
  add(...classes) {
    return this.process(1, classes);
  }
  /**
   * Remove CSS class(es)
   * ```javascript
   * myClassList.remove('bold', 'small');
   * ```
   * @param {String} classes CSS classes to remove
   */
  remove(...classes) {
    return this.process(0, classes);
  }
  /**
   * Toggles the passed CSS class name.
   *
   * If the `force` parameter is passed, `true` means add the class name, `false` means remove it.
   *
   * ```javascript
   * myClassList.toggle('bold', isImportant);
   * ```
   * @param {String} className CSS class to toggle
   * @param {Boolean} [force] `true` to add the class, `false` to remove it.
   * @returns {Boolean} `true` if the operation changed the value.
   */
  toggle(className, flag = Boolean(!this[className])) {
    flag = Boolean(flag);
    if (Boolean(this[className]) !== flag) {
      this[className] = flag;
      delete this[valueSymbol];
      return true;
    }
  }
  /**
   * Analogous to the `String#split` method, but with no delimiter
   * parameter. This method returns an array containing the individual
   * CSS class names set.
   * @returns {String[]} The individual class names in this `DomClassList`
   */
  split() {
    return Objects.getTruthyKeys(this);
  }
  forEach(fn2) {
    return Objects.getTruthyKeys(this).forEach(fn2);
  }
};
DomClassList.prototype[valueSymbol] = null;
DomClassList._$name = "DomClassList";

// ../Core/lib/Core/helper/EventHelper.js
var touchProperties = [
  "clientX",
  "clientY",
  "pageX",
  "pageY",
  "screenX",
  "screenY"
];
var isOption = {
  element: 1,
  thisObj: 1,
  once: 1,
  delegate: 1,
  delay: 1,
  capture: 1,
  passive: 1,
  throttled: 1,
  autoDetach: 1,
  expires: 1,
  block: 1,
  raw: 1,
  fromGlobal: 1
};
var normalizedKeyNames = {
  Spacebar: "Space",
  Del: "Delete",
  Esc: "Escape",
  Left: "ArrowLeft",
  Up: "ArrowUp",
  Right: "ArrowRight",
  Down: "ArrowDown"
};
var specialKeys = {
  Control: "ctrl",
  Alt: "alt",
  Shift: "shift",
  Meta: "meta"
};
var specialKeyRe = /^(ctrl|shift|alt|meta)$/;
var eventProps = [
  "altKey",
  "bubbles",
  "button",
  "buttons",
  "cancelable",
  "clientX",
  "clientY",
  "ctrlKey",
  "layerX",
  "layerY",
  "metaKey",
  "pageX",
  "pageY",
  "returnValue",
  "screenX",
  "screenY",
  "shiftKey"
];
var powerSet = (...names2) => names2.reduce((a, v) => {
  for (let i = 0, n = a.length; i < n; ++i) {
    a.push(i ? `${a[i]}+${v}` : v);
  }
  return a;
}, [""]);
var letterKeyNames = Object.fromEntries("abcdefghijklmnopqrstuvwxyz".split("").map((c) => [c, c.toUpperCase()]));
var modifierKeys = powerSet("Ctrl", "Alt", "Meta", "Shift");
var getModKeys = (ev) => modifierKeys[(ev.ctrlKey && 1) | (ev.altKey && 2) | (ev.metaKey && 4) | (ev.shiftKey && 8)];
var getKeyName = (event) => {
  const parts = [getModKeys(event)], { code, key } = event;
  parts[parts[0] ? 1 : 0] = (code == null ? void 0 : code.includes("Numpad")) ? code : letterKeyNames[key] || key;
  return parts.join("+");
};
var {
  isSafari,
  isMobileSafari
} = BrowserHelper;
var wrapperMap = /* @__PURE__ */ new WeakMap();
var fixTarget = (target) => (target == null ? void 0 : target.nodeType) === Element.TEXT_NODE ? target.parentElement : target;
var _EventHelper = class _EventHelper {
  static normalizeEvent(event) {
    return ObjectHelper.copyPropertiesIf(event, event.changedTouches[0] || event.touches[0], touchProperties);
  }
  /**
   * For use when synthesizing events from native DOM events. Copies valid properties from the passed
   * event into the destination object;
   * @param {Object} dest Destination object
   * @param {Event} event The event whose properties to copy
   * @returns {Object} An event construction object.
   * @internal
   */
  static copyEvent(dest, event) {
    return ObjectHelper.copyProperties(dest, event, eventProps);
  }
  /**
   * Returns the `[x, y]` coordinates of the event in the viewport coordinate system.
   * @param {Event} event The event
   * @returns {Number[]} The coordinate.
   */
  static getXY(event) {
    if (event.touches) {
      event = event.touches[0];
    }
    return [event.clientX, event.clientY];
  }
  /**
   * Returns the pixel distance between two mouse/touch/pointer events.
   * @param {Event} event1 The first event.
   * @param {Event} event2 The second event.
   * @returns {Number} The distance in pixels between the two events.
   */
  static getDistanceBetween(event1, event2, options) {
    const xy1 = EH.getXY(event1), xy2 = EH.getXY(event2), xDistance = xy1[0] - xy2[0], yDistance = xy1[1] - xy2[1];
    if (options == null ? void 0 : options.x) {
      return xDistance;
    }
    if (options == null ? void 0 : options.y) {
      return yDistance;
    }
    return Math.sqrt(Math.pow(xDistance, 2) + Math.pow(yDistance, 2));
  }
  /**
   * Returns a {@link Core.helper.util.Rectangle.Point} which encapsulates the `pageX/Y` position of the event.
   * May be used in {@link Core.helper.util.Rectangle} events.
   * @param {Event} event A browser mouse/touch/pointer event.
   * @returns {Core.helper.util.Rectangle.Point} The page point.
   */
  static getPagePoint(event) {
    return new Point(event.pageX, event.pageY);
  }
  /**
   * Returns a {@link Core.helper.util.Rectangle.Point} which encapsulates the `clientX/Y` position of the event.
   * May be used in {@link Core.helper.util.Rectangle} events.
   * @param {Event} event A browser mouse/touch/pointer event.
   * @returns {Core.helper.util.Rectangle.Point} The page point.
   */
  static getClientPoint(event) {
    return new Point(event.clientX, event.clientY);
  }
  /**
   * Add a listener or listeners to an element
   * The `options` parameter allows supplying options for the listener(s), for available options see {@link #typedef-ElementListenerConfig}.
   *
   * @param {EventTarget} element The event target to add a listener/listeners to.
   * @param {String|Object} eventName Either a string, being the name of the event to listen for,
   * or an options object containing event names and options as keys. See the options parameter
   * for details, or the {@link #function-on-static} method for details.
   * @param {Function} [handler] If the second parameter is a string event name, this is the handler function.
   * @param {ElementListenerConfig} [options] If the second parameter is a string event name, this is the options.
   * @returns {Function} A detacher function which removes all the listeners when called.
   */
  static addListener(element, eventName, handler, options) {
    if (element.nodeType) {
      if (typeof eventName === "string") {
        options = Object.assign({
          element,
          [eventName]: handler
        }, options);
      } else {
        options = Object.assign({
          element
        }, eventName);
      }
    } else {
      options = element;
    }
    return EH.on(options);
  }
  /**
   * Adds a listener or listeners to an element.
   * all property names other than the options listed below are taken to be event names,
   * and the values as handler specs.
   *
   * A handler spec is usually a function reference or the name of a function in the `thisObj`
   * option.
   *
   * But a handler spec may also be an options object containing a `handler` property which is
   * the function or function name, and local options, including `element` and `thisObj`
   * which override the top level options.
   *
   * The `options` parameter allows supplying options for the listener(s), for available options see {@link #typedef-ElementListenerConfig}.
   *
   *  Usage example
   *
   * ```javascript
   * construct(config) {
   *     super.construct(config);
   *
   *     // Add auto detaching event handlers to this Widget's reference elements
   *     EventHelper.on({
   *         element : this.iconElement,
   *         click   : '_handleIconClick',
   *         thisObj : this,
   *         contextmenu : {
   *             element : document,
   *             handler : '_handleDocumentContextMenu'
   *         }
   *     });
   * }
   * ```
   *
   * The `click` handler on the `iconElement` calls `this._handleIconClick`.
   *
   * The `contextmenu` handler is added to the `document` element, but the `thisObj`
   * is defaulted in from the top `options` and calls `this._handleDocumentContextMenu`.
   *
   * Note that on touch devices, `dblclick` and `contextmenu` events are synthesized.
   * Synthesized events contain a `browserEvent` property containing the final triggering
   * event of the gesture. For example a synthesized `dblclick` event would contain a
   * `browserEvent` property which is the last `touchend` event. A synthetic `contextmenu`
   * event will contain a `browserEvent` property which the longstanding `touchstart` event.
   *
   * @param {ElementListenerConfig} options The full listener specification.
   * @returns {Function} A detacher function which removes all the listeners when called.
   */
  static on(options) {
    const element = options.element, thisObj = options.thisObj, handlerDetails = [];
    for (const eventName in options) {
      if (!isOption[eventName]) {
        let handlerSpec = options[eventName];
        if (typeof handlerSpec !== "object") {
          handlerSpec = {
            handler: handlerSpec
          };
        }
        const targetElement = handlerSpec.element || element;
        handlerDetails.push(EH.addElementListener(targetElement, eventName, handlerSpec, options));
      }
    }
    const detacher2 = () => {
      for (let handlerSpec, i = 0; i < handlerDetails.length; i++) {
        handlerSpec = handlerDetails[i];
        EH.removeEventListener(handlerSpec[0], handlerSpec[1], handlerSpec[2]);
      }
      handlerDetails.length = 0;
    };
    if (thisObj && options.autoDetach !== false) {
      thisObj.doDestroy = FunctionHelper.createInterceptor(thisObj.doDestroy, detacher2, thisObj);
    }
    return detacher2;
  }
  /**
   * Used internally to add a single event handler to an element.
   * @param {EventTarget} element The event target to add the handler to.
   * @param {String} eventName The name of the event to add a handler for.
   * @param {Function|String|Object} handlerSpec Either a function to call, or
   * the name of a function to call in the `thisObj`, or an object containing
   * the handler local options.
   * @param {Function|String} [handlerSpec.handler] Either a function to call, or
   * the name of a function to call in the `thisObj`.
   * @param {EventTarget} [handlerSpec.element] Optionally a local event target for the listener.
   * @param {Object} [handlerSpec.thisObj] A local `this` specification for the handler.
   * @param {Object} defaults The `options` parameter from the {@link #function-addListener-static} call.
   * @private
   */
  static addElementListener(element, eventName, handlerSpec, defaults) {
    const handler = EH.createHandler(element, eventName, handlerSpec, defaults), { spec } = handler, expires = handlerSpec.expires || defaults.expires, options = spec.capture != null || spec.passive != null ? {
      capture: spec.capture,
      passive: spec.passive
    } : void 0;
    element.addEventListener(eventName, handler, options);
    if (expires) {
      const thisObj = handlerSpec.thisObj || defaults.thisObj, delayable = (thisObj == null ? void 0 : thisObj.isDelayable) ? thisObj : globalThis, { alt: alt2 } = expires, delay2 = alt2 ? expires.delay : expires, { spec: spec2 } = handler;
      spec2.expires = expires;
      spec2.timerId = delayable[typeof delay2 === "number" ? "setTimeout" : "requestAnimationFrame"](() => {
        spec2.timerId = null;
        EH.removeEventListener(element, eventName, handler);
        if (alt2 && !handler.called) {
          (typeof alt2 === "string" ? thisObj[alt2] : alt2).call(thisObj);
        }
      }, delay2, `listener-timer-${performance.now()}`);
    }
    return [element, eventName, handler, options];
  }
  // composedPath throws in salesforce
  // https://github.com/bryntum/support/issues/4432
  static getComposedPathTarget(event) {
    var _a5;
    return event.composedPath()[0] || ((_a5 = event.path) == null ? void 0 : _a5[0]);
  }
  static fixEvent(event, fromGlobal) {
    var _a5, _b2;
    if (event.isBryntumEvent) {
      return event;
    }
    if (wrapperMap.has(event)) {
      return wrapperMap.get(event);
    }
    const { ctrlKey, metaKey, type, target } = event, deepTarget = this.getComposedPathTarget(event), isOpenWC = ((_b2 = (_a5 = deepTarget == null ? void 0 : deepTarget.getRootNode) == null ? void 0 : _a5.call(deepTarget)) == null ? void 0 : _b2.mode) === "open", eventWrapper = {
      set(event2, prop, value2) {
        var _a6;
        if (prop in event2 && ((_a6 = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(event2), "type")) == null ? void 0 : _a6.set)) {
          return Reflect.set(event2, prop, value2);
        } else {
          eventWrapper[prop] = value2;
          return true;
        }
      },
      get(event2, prop) {
        if (ObjectHelper.hasOwn(eventWrapper, prop)) {
          return eventWrapper[prop];
        }
        switch (prop) {
          case "fixed":
          case "isBryntumEvent":
            return true;
          case "key":
            return normalizedKeyNames[event2.key] || event2.key;
          case "code":
            return event2.key === " " && !event2.code ? "Space" : event2.code;
          case "ctrlKey":
            return BrowserHelper.isMac && metaKey !== ctrlKey ? metaKey : ctrlKey;
          case "metaKey":
            return BrowserHelper.isMac && metaKey !== ctrlKey ? ctrlKey : metaKey;
          case "virtualKey":
            return getKeyName(result);
          case "virtualModKeys":
            return getModKeys(result);
          case "borderOffsetX":
            return !isNaN(event2.offsetX) ? event2.offsetX + (isSafari ? 0 : parseInt(target.ownerDocument.defaultView.getComputedStyle(fixTarget(target)).getPropertyValue("border-left-width"))) : void 0;
          case "borderOffsetY":
            return !isNaN(event2.offsetY) ? event2.offsetY + (isSafari ? 0 : parseInt(target.ownerDocument.defaultView.getComputedStyle(fixTarget(target)).getPropertyValue("border-top-width"))) : void 0;
          case "target":
            return isOpenWC ? deepTarget : fixTarget(event2.target);
          case "relatedTarget":
            return fixTarget(event2.relatedTarget);
          case "originalTarget":
            return target;
          case "deepTarget":
            return deepTarget;
          case "currentTarget":
            return eventWrapper.delegatedTarget || event2.currentTarget;
          case "browserEvent":
            return event2;
          case "timeStamp":
            if (isMobileSafari) {
              eventWrapper.timeStamp || (eventWrapper.timeStamp = Date.now());
            }
          default:
            if (!(prop in eventWrapper)) {
              const result2 = Reflect.get(event2, prop);
              return typeof result2 === "function" ? result2.bind(event2) : result2;
            } else {
              return eventWrapper[prop];
            }
        }
      },
      has(event2, key) {
        return key in event2 || key in eventWrapper;
      }
    };
    const result = new Proxy(event, eventWrapper);
    wrapperMap.set(event, result);
    if (type.startsWith("touch")) {
      this.normalizeEvent(result);
    }
    if (!fromGlobal) {
      switch (type) {
        case "keydown":
          GlobalEvents_default.currentKeyDown = result;
          break;
        case "mousedown":
          GlobalEvents_default.currentMouseDown = result;
          break;
        case "pointerdown":
          GlobalEvents_default.currentPointerDown = result;
          break;
        case "touchstart":
          GlobalEvents_default.currentTouch = result;
      }
    }
    return result;
  }
  static createHandler(element, eventName, handlerSpec, defaults) {
    const delay2 = handlerSpec.delay || defaults.delay, throttled = handlerSpec.throttled || defaults.throttled, block = handlerSpec.block || defaults.block, once = "once" in handlerSpec ? handlerSpec.once : defaults.once, capture = "capture" in handlerSpec ? handlerSpec.capture : defaults.capture, passive = "passive" in handlerSpec ? handlerSpec.passive : defaults.passive, delegate = "delegate" in handlerSpec ? handlerSpec.delegate : defaults.delegate, fromGlobal = "fromGlobal" in handlerSpec ? handlerSpec.fromGlobal : defaults.fromGlobal, wrappedFn = handlerSpec.handler, expires = handlerSpec.expires, thisObj = handlerSpec.thisObj || defaults.thisObj;
    let handler = (event, ...args) => {
      if (EH.playingDemo && event.isTrusted) {
        return;
      }
      if (thisObj == null ? void 0 : thisObj.isDestroyed) {
        return;
      }
      handler.called = true;
      (typeof wrappedFn === "string" ? thisObj[wrappedFn] : wrappedFn).call(thisObj, EH.fixEvent(event, fromGlobal), ...args);
    };
    if (block) {
      const wrappedFn2 = handler;
      let lastCallTime, lastTarget;
      handler = (e, ...args) => {
        const now2 = performance.now();
        if (!lastCallTime || e.target !== lastTarget || now2 - lastCallTime > block) {
          lastTarget = e.target;
          lastCallTime = now2;
          wrappedFn2(e, ...args);
        }
      };
    }
    if (delay2 != null) {
      const wrappedFn2 = handler, delayable = (thisObj == null ? void 0 : thisObj.setTimeout) ? thisObj : globalThis;
      handler = (...args) => {
        delayable.setTimeout(() => {
          wrappedFn2(...args);
        }, delay2);
      };
    }
    if (throttled != null) {
      let alt2, buffer = throttled;
      if (throttled.buffer) {
        alt2 = (e) => {
          return throttled.alt.call(EH, EH.fixEvent(e, fromGlobal));
        };
        buffer = throttled.buffer;
      }
      if (thisObj == null ? void 0 : thisObj.isDelayable) {
        handler = thisObj.throttle(handler, {
          delay: buffer,
          throttled: alt2
        });
      } else {
        handler = FunctionHelper.createThrottled(handler, buffer, thisObj, null, alt2);
      }
    }
    if (once) {
      const wrappedFn2 = handler;
      handler = (...args) => {
        EH.removeEventListener(element, eventName, handler);
        wrappedFn2(...args);
      };
    }
    if (delegate) {
      const wrappedFn2 = handler;
      handler = (event, ...args) => {
        var _a5, _b2;
        event = EH.fixEvent(event, fromGlobal);
        const delegatedTarget = (_b2 = (_a5 = event.target).closest) == null ? void 0 : _b2.call(_a5, delegate);
        if (!delegatedTarget) {
          return;
        }
        event.delegatedTarget = delegatedTarget;
        wrappedFn2(event, ...args);
      };
    }
    if (handlerSpec.thisObj && handlerSpec.autoDetach !== false) {
      thisObj.doDestroy = FunctionHelper.createInterceptor(thisObj.doDestroy, () => EH.removeEventListener(element, eventName, handler), thisObj);
    }
    handler.spec = {
      delay: delay2,
      throttled,
      block,
      once,
      thisObj,
      capture,
      expires,
      passive,
      delegate
    };
    return handler;
  }
  static removeEventListener(element, eventName, handler) {
    const { expires, timerId, thisObj, capture } = handler.spec;
    if ((expires == null ? void 0 : expires.alt) && timerId) {
      const delayable = (thisObj == null ? void 0 : thisObj.isDelayable) ? thisObj : globalThis;
      delayable[typeof expires.delay === "number" ? "clearTimeout" : "cancelAnimationFrame"](timerId);
    }
    element.removeEventListener(eventName, handler, capture);
  }
  /**
   * Calls a callback when the described animation completes.
   *
   * @param {Object} detail
   * @param {HTMLElement} detail.element The element which is being animated.
   * @param {String|RegExp} [detail.animationName] The name of the animation to wait for.
   * @param {String} [detail.property] If no `animationName` specified, the CSS property
   * which is being animated.
   * @param {Function} detail.handler The function to call on animation end.
   * @param {Number} [detail.duration] Optional fallback time to wait until calling the callback.
   * @param {Object} [detail.thisObj] The `this` reference to call the callback with.
   * @param {Array} [detail.args] Optional arguments to call the callback with.
   * @param {Core.mixin.Delayable} [detail.timerSource] A Delayable to provide the fallback timeout.
   * @param {Boolean} [detail.runOnDestroy] If `timerSource` is a {@link Core.mixin.Delayable},
   * `true` to invoke the callback if it is destroyed during the animation.
   * @returns {Function} a function which detaches the animation end listener.
   */
  static onTransitionEnd({
    element,
    animationName,
    property,
    handler,
    mode = animationName ? "animation" : "transition",
    duration = DomHelper[`get${mode === "transition" ? "Property" : ""}${StringHelper.capitalize(mode)}Duration`](element, property),
    timeout,
    thisObj = globalThis,
    args = [],
    timerSource,
    runOnDestroy
  }) {
    let timerId, maxDurationTimerId;
    timerSource = timerSource || (thisObj.isDelayable ? thisObj : globalThis);
    const { isDelayable } = timerSource, now2 = performance.now(), transitionTimerId = `transitionTimer${now2}`, timeoutTimerId = `timeoutTimer${now2}`, callbackArgs = [element, property, ...args], doCallback = () => {
      var _a5;
      detacher2();
      if (!thisObj.isDestroyed) {
        if ((_a5 = thisObj.$meta) == null ? void 0 : _a5.class.isBase) {
          thisObj.callback(handler, thisObj, callbackArgs);
        } else {
          handler.apply(thisObj, callbackArgs);
        }
      }
    }, detacher2 = EH.on({
      element,
      [`${mode}end`]({ animationName: endedAnimation, propertyName, target }) {
        var _a5, _b2;
        if (target === element) {
          if (propertyName === property || (endedAnimation == null ? void 0 : endedAnimation.match(animationName))) {
            if (timerId) {
              if (maxDurationTimerId) {
                (_a5 = timerSource.clearTimeout) == null ? void 0 : _a5.call(timerSource, isDelayable ? timeoutTimerId : maxDurationTimerId);
              }
              (_b2 = timerSource.clearTimeout) == null ? void 0 : _b2.call(timerSource, isDelayable ? transitionTimerId : timerId);
              timerId = null;
            }
            doCallback();
          }
        }
      }
    });
    if (timeout) {
      maxDurationTimerId = timerSource.setTimeout(doCallback, Math.max(timeout, (duration || 0) + 100), timeoutTimerId);
    }
    if (duration != null) {
      timerId = timerSource.setTimeout(doCallback, duration + 50, transitionTimerId, runOnDestroy);
    }
    return detacher2;
  }
  /**
   * Waits for the described animation completes.
   *
   * @param {Object} config
   * @param {HTMLElement} config.element The element which is being animated.
   * @param {String|RegExp} [config.animationName] The name of the animation to wait for.
   * @param {String} [config.property] If no `animationName` specified, the CSS property
   * which is being animated.
   * @param {Number} [config.duration] Optional fallback time to wait until calling the callback.
   * @param {Core.mixin.Delayable} [config.timerSource] A Delayable to provide the fallback timeout.
   * @param {Boolean} [config.runOnDestroy] If `timerSource` is a {@link Core.mixin.Delayable},
   * `true` to invoke the callback if it is destroyed during the animation.
   * @async
   */
  static async waitForTransitionEnd(config) {
    return new Promise((resolve) => {
      config.handler = resolve;
      _EventHelper.onTransitionEnd(config);
    });
  }
  /**
   * Private function to wrap the passed function. The returned wrapper function to be used as
   * a `touchend` handler which will call the passed function passing a fabricated `dblclick`
   * event if there is a `click` within 300ms.
   * @param {Element} element element
   * @param {String|Function} handler The handler to call.
   * @param {Object} thisObj The owner of the function.
   * @private
   */
  static createDblClickWrapper(element, handler, thisObj) {
    let startId, secondListenerDetacher, tapholdTimer;
    return () => {
      if (!secondListenerDetacher) {
        secondListenerDetacher = EH.on({
          element,
          // We only get here if a touchstart arrives within 300ms of a click
          touchstart: (secondStart) => {
            startId = secondStart.changedTouches[0].identifier;
            secondStart.preventDefault();
          },
          touchend: (secondClick) => {
            if (secondClick.changedTouches[0].identifier === startId) {
              secondClick.preventDefault();
              clearTimeout(tapholdTimer);
              startId = secondListenerDetacher = null;
              const targetRect = Rectangle.from(secondClick.changedTouches[0].target, null, true), offsetX = secondClick.changedTouches[0].pageX - targetRect.x, offsetY = secondClick.changedTouches[0].pageY - targetRect.y, dblclickEventConfig = Object.assign({
                browserEvent: secondClick
              }, secondClick), dblclickEvent = new MouseEvent("dblclick", dblclickEventConfig);
              Object.defineProperties(dblclickEvent, {
                target: { value: secondClick.target },
                offsetX: { value: offsetX },
                offsetY: { value: offsetY }
              });
              if (typeof handler === "string") {
                handler = thisObj[handler];
              }
              handler.call(thisObj, dblclickEvent);
            }
          },
          once: true
        });
        tapholdTimer = setTimeout(() => {
          secondListenerDetacher();
          startId = secondListenerDetacher = null;
        }, EH.dblClickTime);
      }
    };
  }
  /**
   * Handles various inputs to figure out the name of the special key of the event.
   *
   * ```javascript
   * EventHelper.toSpecialKey('ctrl') // 'ctrlKey'
   * EventHelper.toSpecialKey(true)   // 'ctrlKey'
   * EventHelper.toSpecialKey(false)  // false
   * EventHelper.toSpecialKey('foo')  // false
   * ```
   *
   * @param {*} value User input value to process.
   * @param {String} defaultValue Default value to fall back to if `true` value is passed.
   * @returns {Boolean|String} Returns `false` if provided value cannot be converted to special key and special key
   * name otherwise.
   * @internal
   */
  static toSpecialKey(value2, defaultValue2 = "ctrlKey") {
    let result = false;
    if (value2 === true) {
      result = defaultValue2;
    } else if (typeof value2 === "string") {
      value2 = value2.toLowerCase();
      if (value2.match(specialKeyRe)) {
        result = `${value2}Key`;
      }
    }
    return result;
  }
  /**
   * If keyup event is triggered when special key is pressed, we don't get special key value from properties like
   * `ctrlKey`. Instead we need to read `event.key`. That property uses full name and we use abbreviations, so we
   * need to convert the key.
   * @param {String} code
   * @returns {String}
   * @internal
   */
  static specialKeyFromEventKey(code) {
    return specialKeys[code] || "no-special-key";
  }
};
__publicField(_EventHelper, "$name", "EventHelper");
/**
 * DOM event to trigger name mapping.
 * @internal
 */
__publicField(_EventHelper, "eventNameMap", {
  mousedown: "MouseDown",
  mouseup: "MouseUp",
  click: "Click",
  dblclick: "DblClick",
  contextmenu: "ContextMenu",
  mouseenter: "MouseEnter",
  mouseleave: "MouseLeave",
  mousemove: "MouseMove",
  mouseover: "MouseOver",
  mouseout: "MouseOut",
  keyup: "KeyUp",
  keydown: "KeyDown",
  keypress: "KeyPress"
});
var EventHelper = _EventHelper;
Helpers_default.register(EventHelper);
var EH = EventHelper;
EH.longPressTime = 700;
EH.dblClickTime = 300;
if (BrowserHelper.isTouchDevice) {
  EH.on({
    element: document,
    touchmove: (event) => {
      if (event.target.closest(".b-dragging")) {
        event.preventDefault();
      }
    },
    passive: false,
    capture: true
  });
}
EventHelper._$name = "EventHelper";

// ../Core/lib/Core/helper/DomHelper.js
var DEFAULT_FONT_SIZE = 14;
var t0t0 = { align: "t0-t0" };
var ELEMENT_NODE = Node.ELEMENT_NODE;
var TEXT_NODE = Node.TEXT_NODE;
var textualInputs = {
  text: 1,
  password: 1,
  date: 1,
  search: 1,
  email: 1,
  number: 1,
  time: 1,
  "datetime-local": 1,
  tel: 1,
  url: 1,
  month: 1,
  week: 1
};
var { isObject: isObject2 } = ObjectHelper;
var numberRe = /[+-]?\d*\.?\d+[eE]?-?\d*/g;
var numberReSrc = numberRe.source;
var translateMatrix2dRe = new RegExp(`matrix\\((?:${numberReSrc}),\\s?(?:${numberReSrc}),\\s?(?:${numberReSrc}),\\s?(?:${numberReSrc}),\\s?(${numberReSrc}),\\s?(${numberReSrc})`);
var translateMatrix3dRe = new RegExp(`matrix3d\\((?:-?\\d*),\\s?(?:-?\\d*),\\s?(?:-?\\d*),\\s?(?:-?\\d*),\\s?(?:-?\\d*),\\s?(?:-?\\d*),\\s?(?:-?\\d*),\\s?(?:-?\\d*),\\s?(?:-?\\d*),\\s?(?:-?\\d*),\\s?(?:-?\\d*),\\s?(?:-?\\d*),\\s?(-?\\d*),\\s?(-?\\d*)`);
var translateMatrixRe = new RegExp(`(?:${translateMatrix2dRe.source})|(?:${translateMatrix3dRe.source})`);
var pxTtranslateXRe = new RegExp(`translate(3d|X)?\\((${numberReSrc})px(?:,\\s?(${numberReSrc})px)?`);
var pxTtranslateYRe = new RegExp(`translate(3d|Y)?\\((${numberReSrc})px(?:,\\s?(${numberReSrc})px)?`);
var whiteSpaceRe2 = /\s+/;
var semicolonRe = /\s*;\s*/;
var colonRe = /\s*:\s*/;
var digitsRe = /^-?((\d+(\.\d*)?)|(\.?\d+))$/;
var elementPropKey = "$bryntum";
var focusabilityCache = {};
var elementCreateExpandos = {
  elementData: "",
  for: "htmlFor",
  retainElement: ""
};
var elementCreateProperties = {
  // these two are handled by being in elementCreateExpands:
  // elementData  : 1,
  // for          : 1,
  tag: 1,
  html: 1,
  text: 1,
  children: 1,
  tooltip: 1,
  style: 1,
  dataset: 1,
  parent: 1,
  nextSibling: 1,
  ns: 1,
  reference: 1,
  class: 1,
  className: 1,
  unmatched: 1,
  // Used by syncId approach
  onlyChildren: 1,
  // Used by sync to not touch the target element itself,
  listeners: 1,
  // eslint-disable-line bryntum/no-listeners-in-lib
  compareHtml: 1,
  // Sync
  syncOptions: 1,
  // Sync
  keepChildren: 1
  // Sync
};
var focusabilities = [
  ["f", "focusable"],
  ["n", "natural"],
  ["p", "programmatic"],
  ["t", "tabbable"]
];
var focusabilityDecode = (f) => Object.freeze(Object.fromEntries(focusabilities.map(([c, k]) => [k, f.includes(c)])));
var styleIgnoreProperties = {
  length: 1,
  parentRule: 1,
  style: 1
};
var nativeEditableTags = {
  INPUT: 1,
  TEXTAREA: 1
};
var nativeFocusableTags = {
  BUTTON: 1,
  IFRAME: 1,
  EMBED: 1,
  INPUT: 1,
  OBJECT: 1,
  SELECT: 1,
  TEXTAREA: 1,
  BODY: 1
};
var win = globalThis;
var doc = document;
var emptyObject3 = Object.freeze({});
var arraySlice = Array.prototype.slice;
var immediatePromise = Promise.resolve();
var fontProps = [
  "font-size",
  "font-size-adjust",
  "font-style",
  "font-weight",
  "font-family",
  "font-kerning",
  "font-stretch",
  "line-height",
  "text-transform",
  "text-decoration",
  "letter-spacing",
  "word-break"
];
var isHiddenWidget = (e) => e._hidden;
var mergeChildren = (dest, src, options) => {
  if (options.key === "children") {
    return ObjectHelper.mergeItems(dest, src, options);
  }
  return ObjectHelper.blend(dest, src, options);
};
var isVisible = (e, skipVisibility) => {
  if (e.isConnected) {
    const style = e.ownerDocument.defaultView.getComputedStyle(e);
    return style.getPropertyValue("display") !== "none" && (skipVisibility || style.getPropertyValue("visibility") !== "hidden");
  }
};
var hasLayout = (el) => el && el.isConnected && (el === doc.body || Boolean(el.offsetParent) || (el.ownerDocument.defaultView && ("offsetParent" in el && DomHelper.getStyleValue(el, "position") !== "fixed") ? el.offsetParent : hasLayout(el.parentNode)));
var elementOrConfigToElement = (elementOrConfig) => {
  if (elementOrConfig instanceof Node) {
    return elementOrConfig;
  }
  if (typeof elementOrConfig === "string") {
    return DH.createElementFromTemplate(elementOrConfig);
  }
  return DH.createElement(elementOrConfig);
};
var canonicalStyles = /* @__PURE__ */ Object.create(null);
var canonicalizeStyle = (name, hasUnit) => {
  const entry = canonicalStyles[name] || [StringHelper.hyphenate(name), hasUnit];
  if (!canonicalStyles[name]) {
    canonicalStyles[entry[0]] = canonicalStyles[name] = entry;
  }
  return entry;
};
var slideInAnimationName = /b-slide-in-from-\w+/;
var getParent = (node, layoutParent) => {
  if (node) {
    const result = (node.ownerSVGElement || node)[layoutParent && DomHelper.getStyleValue(node, "position") !== "static" ? "offsetParent" : "parentNode"] || getParent(node.target, layoutParent);
    return (result == null ? void 0 : result.nodeType) === Node.DOCUMENT_FRAGMENT_NODE ? result.host : result;
  }
};
var isElement = (node) => (node == null ? void 0 : node.nodeType) === Node.ELEMENT_NODE;
var getAnimatedElements = (within = doc) => {
  var _a5;
  return ((_a5 = within.getAnimations()) == null ? void 0 : _a5.reduce((result, { effect, playState }) => {
    if (playState === "running" && effect.getTiming().iterations !== Infinity) {
      result.push(effect.target);
    }
    return result;
  }, [])) || [];
};
[
  "top",
  "right",
  "bottom",
  "left",
  "width",
  "height",
  "maxWidth",
  "maxHeight",
  "minWidth",
  "minHeight",
  "borderSpacing",
  "borderWidth",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "insetBlockEnd",
  "insetBlockStart",
  "insetInlineEnd",
  "insetInlineStart",
  "marginTop",
  "marginRight",
  "marginBottom",
  "marginLeft",
  "marginInlineStart",
  "marginInlineEnd",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "fontSize",
  "letterSpacing",
  "lineHeight",
  "outlineWidth",
  "textIndent",
  "wordSpacing",
  "insetInlineStart",
  "insetBlockStart",
  "insetInlineEnd",
  "insetBlockEnd"
].forEach((name) => canonicalizeStyle(name, true));
var scrollBarWidth = null;
var idCounter = 0;
var themeInfo = null;
var templateElement;
var htmlParser;
var scrollBarMeasureElement;
var _DomHelper = class _DomHelper {
  static get devicePixelRatio() {
    let result = globalThis.devicePixelRatio;
    if (result == null) {
      const STEP = 0.05, MAX = 5, MIN = 0.5, mediaQuery = (v) => `(-webkit-min-device-pixel-ratio: ${v}),
                (min--moz-device-pixel-ratio: ${v}),
                (min-resolution: ${v}dppx)`;
      for (let i = MAX * 100; i >= MIN * 100; i -= STEP * 100) {
        if (globalThis.matchMedia(mediaQuery(i / 100)).matches) {
          result = i / 100;
          break;
        }
      }
    }
    return result;
  }
  static get usingKeyboard() {
    return DH._usingKeyboard;
  }
  static set usingKeyboard(value2) {
    return DH._usingKeyboard = value2;
  }
  static setUsingKeyboard(element, value2) {
    DH.setFocusRendition(element, value2);
    DH._usingKeyboard = value2;
  }
  static setFocusRendition(element, value2) {
    var _a5;
    if (DH.enableKeyboardCssModifiers) {
      if (element.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
        if (value2) {
          Array.from(element.children).forEach((el) => {
            var _a6;
            return ((_a6 = el.matches) == null ? void 0 : _a6.call(el, ".b-outer")) && el.classList.add("b-using-keyboard");
          });
        } else {
          DH.removeClsGlobally(element, "b-using-keyboard");
        }
      } else {
        (_a5 = element.classList) == null ? void 0 : _a5.toggle("b-using-keyboard", value2);
      }
    }
  }
  static findParent(e, p, limit) {
    return this.closest(e == null ? void 0 : e.parentNode, p, limit);
  }
  static closest(e, p, limit) {
    if (e && e !== limit) {
      return p(e) ? e : this.closest(e.parentNode, p, limit);
    }
  }
  /**
   * Animates the specified element to slide it into view within the visible viewport
   * of its parentElement from the direction of movement.
   *
   * So in a left-to-right Widget, `direction` 1 means it slides in from the right
   * and `direction` -1 means it slides in from the left. RTL reverses the movement.
   *
   * See the forward/backward navigations in {@link Core.widget.DatePicker} for an example
   * of this in action.
   *
   * If "next" should arrive from below and "previous" should arrive from above, add the
   * class `b-slide-vertical` to the element.
   * @param {HTMLElement} element The element to slide in.
   * @param {Number} direction
   * * `1` to slide in from the "next" direction.
   * * `-1` to slide in from the "previous" direction.
   *
   * If the element is inside an RTL widget the directions are reversed.
   * @async
   */
  static async slideIn(element, direction = 1) {
    const cls = `b-slide-in-${direction > 0 ? "next" : "previous"}`, {
      classList,
      parentNode: { classList: pClassList }
    } = element;
    pClassList.add("b-sliding-child-element");
    classList.add(cls);
    await EventHelper.waitForTransitionEnd({
      element,
      animationName: slideInAnimationName
    });
    classList.remove(cls);
    pClassList.remove("b-sliding-child-element");
  }
  /**
   * Returns an object describing the focus and keyboard navigation aspects of a given element.
   * @param {HTMLElement} element The element to examine.
   * @returns {Focusability}
   * @internal
   */
  static getFocusability(element) {
    let focusability = "";
    if (element && !element.hasAttribute("inert")) {
      const { nodeName } = element, tabIndex = element.getAttribute("tabIndex");
      focusability = nativeFocusableTags[nodeName] || (nodeName === "A" || nodeName === "LINK") && element.href || element.contentEditable === "true" ? "n" : "";
      if (tabIndex === "-1") {
        focusability += "fp";
      } else if (focusability || tabIndex != null) {
        focusability += "ft";
      }
    }
    return focusabilityCache[focusability] || (focusabilityCache[focusability] = focusabilityDecode(focusability));
  }
  /**
   * Returns `true` if the passed element is focusable either programmatically or through pointer gestures.
   * @param {HTMLElement} element The element to test.
   * @returns {Boolean} Returns `true` if the passed element is focusable
   */
  static isFocusable(element, skipAccessibilityCheck) {
    if (element && !skipAccessibilityCheck) {
      if (!DH.isVisible(element) || DH.Widget.fromElement(element, isHiddenWidget)) {
        return false;
      }
    }
    return DH.getFocusability(element).focusable;
  }
  /**
   * Returns `true` if the passed element accepts keystrokes to edit its contents.
   * @returns {Boolean} Returns `true` if the passed element is editable.
   */
  static isEditable(element, onlyTextInputs) {
    return Boolean((element.isContentEditable || nativeEditableTags[element.nodeName]) && (!onlyTextInputs || (element.nodeName !== "INPUT" || textualInputs[element.type.toLowerCase()])) && !(element.readOnly || element.disabled || element.closest("[inert]")));
  }
  static isTextualInput(element) {
    return nativeEditableTags[element.nodeName] && (element.nodeName !== "INPUT" || textualInputs[element.type.toLowerCase()]);
  }
  /**
   * Returns the rectangle of the element or event which is currently visible in the browser viewport,
   * i.e. user can find it on screen, or `false` if it is scrolled out of view.
   * @param {HTMLElement|Event} target The element or event to test.
   * @param {Boolean} [whole=false] Whether to check that whole element is visible, not just part of it.
   * If this is passed as true, the result will be a boolean, `true` or `false`.
   * @privateparam {Core.widget.Widget} [caller] the Widget aligning to the target.
   * @returns {Core.helper.util.Rectangle|Boolean} Returns the rectangle of the element which is currently visible in
   * the browser viewport, or `false` if it is out of view.
   */
  static isInView(target, whole = false, caller) {
    if (!target || !hasLayout(isElement(target.target) ? target.target : target)) {
      return false;
    }
    const positioned = (caller == null ? void 0 : caller.positioned) && _DomHelper.getStyleValue(caller.element, "position") !== "fixed", docRect = Rectangle.from(globalThis), method = whole ? "contains" : "intersect", cOp = positioned && caller.element.offsetParent, cOpR = positioned && Rectangle.from(cOp);
    docRect.height = doc.scrollingElement.scrollHeight;
    if (target === doc.body) {
      return docRect;
    }
    const result = this.getViewportIntersection(target, docRect, method);
    if (result && positioned) {
      result.translate(doc.scrollingElement.scrollLeft, doc.scrollingElement.scrollTop);
    }
    return positioned && result ? result.translate(-cOpR.x + cOp.scrollLeft, -cOpR.y + cOp.scrollTop) : result;
  }
  /**
   * This method checks that the passed target is visible in all viewports
   * @param {HTMLElement|Event} target The HTML element or Event to check for intersection with all ancestor viewports.
   * @param {Core.helper.util.Rectangle} docRect Window rectangle
   * @param {'contains'|'intersect'} method 'contains' or 'intersect'
   * @returns {Core.helper.util.Rectangle}
   */
  static getViewportIntersection(target, docRect, method) {
    var _a5;
    const eventTarget = isElement(target.target) ? target.target : null, elementTarget = eventTarget || target, viewportRect = _DomHelper.getVisibleViewport(getParent(elementTarget, 1), docRect, elementTarget), targetRect = Rectangle.from(elementTarget, null, true);
    if (eventTarget) {
      targetRect.translate(target.offsetX + 1, target.offsetY + 1);
      targetRect.height = targetRect.width = 0;
    }
    return (_a5 = viewportRect == null ? void 0 : viewportRect[method]) == null ? void 0 : _a5.call(viewportRect, targetRect, false, true);
  }
  static getVisibleViewport(startElement, docRect, targetEl) {
    const { fullscreenElement } = doc;
    let result = docRect, add, done, outermostViewport, style, viewport;
    for (viewport = startElement; !done && viewport && viewport !== doc.body; viewport = getParent(viewport, 1)) {
      if (!(add = done = viewport === fullscreenElement)) {
        style = viewport.ownerDocument.defaultView.getComputedStyle(viewport);
        add = (style.overflowX !== "visible" || style.overflowY !== "visible") && style.display !== "contents";
      }
      if (add) {
        const r = Rectangle.client(viewport, null, true, false, targetEl, false);
        if (!r.height || !r.width) {
          return false;
        }
        result = (result == null ? void 0 : result.intersect(r)) || r;
        outermostViewport = viewport;
      }
    }
    if (outermostViewport) {
      result = Rectangle.clipStickies(result, outermostViewport, null, true, targetEl);
    }
    return result;
  }
  /**
   * Returns `true` if the passed element is deeply visible. Meaning it is not hidden using `display`
   * or `visibility` and no ancestor node is hidden.
   * @param {HTMLElement} element The element to test.
   * @returns {Boolean} `true` if deeply visible.
   */
  static isVisible(element) {
    var _a5;
    let skipVisibility = false;
    for (; element; element = getParent(element)) {
      if (element.nodeType === Element.DOCUMENT_NODE) {
        return true;
      }
      if (element.nodeType === Element.ELEMENT_NODE) {
        const style = (_a5 = element.ownerDocument) == null ? void 0 : _a5.defaultView.getComputedStyle(element);
        skipVisibility = skipVisibility || style.getPropertyValue("visibility") === "visible";
        if (isElement(element) && !isVisible(element, skipVisibility)) {
          return false;
        }
      }
    }
    return false;
  }
  /**
   * Returns true if DOM Event instance is passed. It is handy to override to support Locker Service.
   * @param event
   * @internal
   * @returns {Boolean}
   */
  static isDOMEvent(event) {
    return event instanceof Event;
  }
  /**
   * Merges specified source DOM config objects into a `dest` object.
   * @param {DomConfig} dest The destination DOM config object.
   * @param {...DomConfig} sources The DOM config objects to merge into `dest`.
   * @returns {DomConfig} The `dest` object.
   * @internal
   */
  static merge(dest, ...sources) {
    return ObjectHelper.blend(dest, sources, { merge: mergeChildren });
  }
  /**
   * Updates in-place a DOM config object whose `children` property may be an object instead of the typical array.
   * The keys of such objects become the `reference` property upon conversion.
   *
   * @param {DomConfig} domConfig
   * @param {Function} [namedChildren] A function to call for each named child element.
   * @privateparam {Boolean} [ignoreRefs] Not meant to be manually set, used when recursing.
   * @returns {DomConfig} Returns the altered DOM config
   * @internal
   */
  static normalizeChildren(domConfig, namedChildren, ignoreRefs) {
    var _a5;
    let children = domConfig == null ? void 0 : domConfig.children, child, i, name, kids, ref;
    if ((_a5 = domConfig == null ? void 0 : domConfig.syncOptions) == null ? void 0 : _a5.ignoreRefs) {
      ignoreRefs = true;
    }
    if (children && !(domConfig instanceof Node)) {
      if (Array.isArray(children)) {
        for (i = 0; i < children.length; ++i) {
          DH.normalizeChildren(children[i], namedChildren, ignoreRefs);
        }
      } else {
        kids = children;
        domConfig.children = children = [];
        for (name in kids) {
          child = kids[name];
          if (child == null ? void 0 : child.isWidget) {
            child = child.element;
          }
          ref = !name.startsWith("$") && !DH.isElement(child);
          ref && (namedChildren == null ? void 0 : namedChildren(
            name,
            /* hoist = */
            !ignoreRefs
          ));
          if (child) {
            if (!(child instanceof Node)) {
              if (child.reference === false) {
                delete child.reference;
              } else if (ref && typeof child !== "string") {
                child.reference = name;
              }
              DH.normalizeChildren(child, namedChildren, ignoreRefs);
            }
            children.push(child);
          }
        }
      }
    }
    return domConfig;
  }
  static roundPx(px, devicePixelRatio = globalThis.devicePixelRatio || 1) {
    const multiplier = 1 / devicePixelRatio;
    return Math.round(px / multiplier) * multiplier;
  }
  // For use when we are dividing a DOM element into even parts. The resulting value
  // must be floored to prevent overflow. But only floored to the device's resolution,
  // so raw Math.floor will not work - it would leave empty space in hi resolution screens.
  static floorPx(px, devicePixelRatio = globalThis.devicePixelRatio || 1) {
    const multiplier = 1 / devicePixelRatio;
    return Math.floor(px * multiplier) / multiplier;
  }
  /**
   * Returns true if element has opened shadow root
   * @param {HTMLElement} element Element to check
   * @returns {Boolean}
   */
  static isCustomElement(element) {
    return Boolean(element == null ? void 0 : element.shadowRoot);
  }
  /**
   * Resolves element from point, checking shadow DOM if required
   * @param {Number} x
   * @param {Number} y
   * @returns {HTMLElement}
   */
  static elementFromPoint(x, y) {
    let el = document.elementFromPoint(x, y);
    if (DH.isCustomElement(el)) {
      el = el.shadowRoot.elementFromPoint(x, y) || el;
    }
    return el;
  }
  /**
   * Resolves child element from point __in the passed element's coordinate space__.
   * @param {HTMLElement} parent The element to find the occupying element in.
   * @param {Number|Core.helper.util.Rectangle.Point} x Either the `X` part of a point, or the point to find.
   * @param {Number} [y] The `Y` part of the point.
   * @returns {HTMLElement}
   * @internal
   */
  static childFromPoint(el, x, y, parent = el) {
    const p = y == null ? x : new Rectangle(x, y, 0, 0);
    let result = null;
    Array.from(el.children).reverse().some((el2) => {
      if (Rectangle.from(el2, parent).contains(p)) {
        result = el2.children.length && DH.childFromPoint(el2, p, null, parent) || el2;
        return true;
      }
    });
    return result;
  }
  /**
   * Converts a name/value pair of a style name and its value into the canonical (hyphenated) name of the style
   * property and a value with the `defaultUnit` suffix appended if no unit is already present in the `value`.
   *
   * For example:
   * ```javascript
   *  const [property, value] = DomHelper.unitize('marginInlineStart', 50);
   *  console.log(property, value);
   * ```
   *
   * ```
   *  > margin-inline-start 50px
   * ```
   * @param {String} name
   * @param {String|Number} value
   * @param {String} [defaultUnit]
   * @returns {String[]}
   * @internal
   */
  static unitize(name, value2, defaultUnit = "px") {
    const [trueName, hasUnits] = canonicalizeStyle(name);
    if (value2 != null) {
      value2 = String(value2);
      value2 = hasUnits && digitsRe.test(value2) ? value2 + defaultUnit : value2;
    }
    return [trueName, value2];
  }
  /**
   * Returns active element checking shadow dom too
   * @readonly
   * @property {HTMLElement}
   */
  static get activeElement() {
    let el = document.activeElement;
    while (el.shadowRoot) {
      el = el.shadowRoot.activeElement;
    }
    return el;
  }
  // returns active element for DOM tree / shadow DOM tree to which element belongs
  static getActiveElement(element) {
    if (element == null ? void 0 : element.isWidget) {
      element = element.element;
    }
    let el = ((element == null ? void 0 : element.getRootNode()) || document).activeElement;
    while (el == null ? void 0 : el.shadowRoot) {
      el = el.shadowRoot.activeElement;
    }
    return el;
  }
  // Returns the visible root (either document.body or a web component shadow root)
  static getRootElement(element) {
    var _a5;
    const root = (_a5 = element.getRootNode) == null ? void 0 : _a5.call(element), { nodeType } = root;
    return nodeType === Node.DOCUMENT_NODE ? root.body : nodeType === Node.DOCUMENT_FRAGMENT_NODE ? root : element.ownerDocument.contains(element) ? element.ownerDocument.body : null;
  }
  // Returns the topmost HTMLElement inside the current context (either document.body or a direct child of a web component shadow root)
  static getOutermostElement(element) {
    var _a5;
    const root = (_a5 = element.getRootNode) == null ? void 0 : _a5.call(element);
    if (root == null ? void 0 : root.body) {
      return root == null ? void 0 : root.body;
    }
    while (element.parentNode !== root && element.parentNode) {
      element = element.parentNode;
    }
    return element;
  }
  static isValidFloatRootParent(element) {
    var _a5;
    return ((_a5 = element.tagName) == null ? void 0 : _a5.toUpperCase()) === "BODY" || // This one allows `rootElement` configured to LWC container element
    element.getRootNode().nodeType === Node.DOCUMENT_FRAGMENT_NODE;
  }
  /**
   * Returns the `id` of the passed element. Generates a unique `id` if the element does not have one.
   * @param {HTMLElement} element The element to return the `id` of.
   */
  static getId(element) {
    return element.id || (element.id = "b-element-" + ++idCounter);
  }
  /**
   * Returns common widget/node ancestor for from/to arguments
   * @param {Core.widget.Widget|HTMLElement} from
   * @param {Core.widget.Widget|HTMLElement} to
   * @returns {Core.widget.Widget|HTMLElement}
   * @internal
   */
  static getCommonAncestor(from, to) {
    var _a5, _b2;
    if (from === to) {
      return from;
    }
    while (from && !(((_a5 = from[from.isWidget ? "owns" : "contains"]) == null ? void 0 : _a5.call(from, to)) || from === to)) {
      from = isElement(from) ? getParent(from) : ((_b2 = from.owner) == null ? void 0 : _b2.isWidget) && from.owner !== from ? from.owner : null;
    }
    return from;
  }
  /**
   * Waits for the specified target element to be visible and in view, and not within an animation.
   *
   * If the specified selector is not immediately visible and outside of any animations, this method
   * will return a Promise which, if the element becomes available in the specified `maxWait`, resolves
   * to the target `HTMLElement`.
   *
   * If the element does not become available after the `maxWait`, the Promise will reject.
   *
   * If the selector ends with `?`, or the `maxWait` is negative, the target element is optional and if not found,
   * `undefined` is returned.
   *
   * @param {String|HTMLElement} target The target element or CSS selector to wait for
   * @param {Number} [maxWait=5000] The maximum time in milliseconds to wait for the target element
   * @param {Number} [waitInterval=100] The interval in milliseconds to check for the target element
   * @returns {HTMLElement|Promise|void} The target element if it is immediately visible
   * and not within an animation. If the target is not immediately available, a Promise that resolves
   * to the target element when it's visible and not within an animation.
   * @typings {HTMLElement|Promise<HTMLElement>|void}
   * @internal
   */
  static waitForSelector(target, maxWait = 5e3, waitInterval = 100) {
    var _a5;
    if (target != null) {
      const optional = (_a5 = target.endsWith) == null ? void 0 : _a5.call(target, "?"), resolvedTarget = target.nodeType ? target : document.querySelector(optional ? target.substring(0, target.length - 1) : target);
      if (optional) {
        if (!resolvedTarget) {
          return;
        }
        if (maxWait > 0) {
          maxWait = -maxWait;
        }
      }
      if (!resolvedTarget || !_DomHelper.isVisible(resolvedTarget) || getAnimatedElements().some((e) => e.contains(resolvedTarget)) || !_DomHelper.isInView(resolvedTarget, false)) {
        return AsyncHelper.waitFor(() => {
          const el = resolvedTarget || document.querySelector(target);
          if (el && _DomHelper.isVisible(el) && !getAnimatedElements().some((e) => e.contains(el)) && _DomHelper.isInView(el, false)) {
            return el;
          }
        }, maxWait, waitInterval);
      }
      return resolvedTarget;
    }
  }
  //region Internal
  /**
   * Internal convenience fn to allow specifying either an element or a CSS selector to retrieve one
   * @private
   * @param {String|HTMLElement} elementOrSelector element or selector to lookup in DOM
   * @returns {HTMLElement}
   */
  static getElement(elementOrSelector) {
    if (typeof elementOrSelector === "string") {
      return doc.querySelector(elementOrSelector);
    }
    return elementOrSelector;
  }
  /**
   * Sets attributes passed as object to given element
   * @param {String|Element} elementOrSelector
   * @param {Object} attributes
   * @internal
   */
  static setAttributes(elementOrSelector, attributes) {
    const element = DH.getElement(elementOrSelector);
    if (element && attributes) {
      for (const key in attributes) {
        if (attributes[key] == null) {
          element.removeAttribute(key);
        } else {
          element.setAttribute(key, attributes[key]);
        }
      }
    }
  }
  static toggleAttribute(elementOrSelector, attribute, force) {
    const element = DH.getElement(elementOrSelector);
    if (element) {
      if (element.toggleAttribute) {
        element.toggleAttribute(attribute, force);
      } else {
        if (force != null) {
          element[force ? "setAttribute" : "removeAttribute"](attribute, "");
        } else {
          element.hasAttribute(attribute) ? element.removeAttribute(attribute) : element.setAttribute(attribute, "");
        }
      }
    }
  }
  /**
   * Adds the passed string as a space-separated value to the passed attribute in the passed element.
   * @param {String|Element} elementOrSelector The element to manipulate
   * @param {String} attribute The attruibute name
   * @param {String} value The value to add
   */
  static addAttributeValue(elementOrSelector, attribute, value2) {
    var _a5;
    const element = DH.getElement(elementOrSelector);
    if (element && value2) {
      element.setAttribute(attribute, [...new Set((_a5 = element.getAttribute(attribute)) == null ? void 0 : _a5.split(" ")).add(value2)].join(" "));
    }
  }
  /**
   * Removed the passed string as a space-separated value from the passed attribute in the passed element.
   * @param {String|Element} elementOrSelector The element to manipulate
   * @param {String} attribute The attruibute name
   * @param {String} value The value to remove
   */
  static removeAttributeValue(elementOrSelector, attribute, value2) {
    var _a5;
    const element = DH.getElement(elementOrSelector);
    if (element && value2) {
      const values = new Set((_a5 = element.getAttribute(attribute)) == null ? void 0 : _a5.split(" "));
      values.delete(value2);
      values.size ? element.setAttribute(attribute, [...values].join(" ")) : element.removeAttribute(attribute);
    }
  }
  /**
   * Sets a CSS [length](https://developer.mozilla.org/en-US/docs/Web/CSS/length) style value.
   * @param {String|HTMLElement} element The element to set the style in, or, if just the result is required,
   * the style magnitude to return with units added. If a nullish value is passed, an empty string
   * is returned.
   * @param {String} [style] The name of a style property which specifies a [length](https://developer.mozilla.org/en-US/docs/Web/CSS/length)
   * @param {Number|String} [value] The magnitude. If a number is used, the value will be set in `px` units.
   * @returns {String} The style value string.
   */
  static setLength(element, style, value2) {
    if (arguments.length === 1) {
      value2 = typeof element === "number" ? `${element}px` : element != null ? element : "";
    } else {
      element = DH.getElement(element);
      value2 = element.style[style] = typeof value2 === "number" ? `${value2}px` : value2 != null ? value2 : "";
    }
    return value2;
  }
  /**
   * Returns string percentified and rounded value for setting element's height, width etc.
   * @param {String|Number} value percent value
   * @param {Number} digits number of decimal digits for rounding
   * @returns {string} percentified value or empty string if value can not be parsed
   * @internal
   */
  static percentify(value2, digits = 2) {
    const mult = Math.pow(10, digits);
    return value2 == null || value2 === "" || isNaN(value2) ? "" : `${Math.round(value2 * mult) / mult}%`;
  }
  //endregion
  //region Children, going down...
  /**
   * Gets the first direct child of `element` that matches `selector`.
   * @param {HTMLElement} element Parent element
   * @param {String} selector CSS selector
   * @returns {HTMLElement}
   * @category Query children
   */
  static getChild(element, selector) {
    return element.querySelector(":scope>" + selector);
  }
  /**
   * Checks if `element` has any child that matches `selector`.
   * @param {HTMLElement} element Parent element
   * @param {String} selector CSS selector
   * @returns {Boolean} true if any child matches selector
   * @category Query children
   */
  static hasChild(element, selector) {
    return DH.getChild(element, selector) != null;
  }
  /**
   * Returns all child elements (not necessarily direct children) that matches `selector`.
   *
   * If `selector` starts with `'>'` or `'# '`, then all components of the `selector` must match inside of `element`.
   * The scope selector, `:scope` is prepended to the selector (and if `#` was used, it is removed).
   *
   * These are equivalent:
   *
   *      DomHelper.children(el, '# .foo .bar');
   *
   *      el.querySelectorAll(':scope .foo .bar');
   *
   * These are also equivalent:
   *
   *      DomHelper.children(el, '> .foo .bar');
   *
   *      el.querySelectorAll(':scope > .foo .bar');
   *
   * @param {HTMLElement} element The parent element
   * @param {String} selector The CSS selector
   * @returns {HTMLElement[]} Matched elements, somewhere below `element`
   * @category Query children
   */
  static children(element, selector) {
    if (selector[0] === ">" || selector.startsWith("# ")) {
      if (selector[0] === "#") {
        selector = selector.substr(2);
      }
      selector = ":scope " + selector;
    }
    return Array.from(element.querySelectorAll(selector));
  }
  // Salesforce doesn't yet support childElementCount. So we replace all native usages with this wrapper and
  // override it for salesforce environment.
  // https://github.com/bryntum/support/issues/3008
  static getChildElementCount(element) {
    return element.childElementCount;
  }
  /**
   * Looks at the specified `element` and all of its children for the one that first matches `selector`.
   * @param {HTMLElement} element Parent element
   * @param {String} selector CSS selector
   * @returns {HTMLElement} Matched element, either element or an element below it
   * @category Query children
   */
  static down(element, selector) {
    var _a5;
    if (!element) {
      return null;
    }
    if ((_a5 = element.matches) == null ? void 0 : _a5.call(element, selector)) {
      return element;
    }
    selector = ":scope " + selector;
    return element.querySelector(selector);
  }
  /**
   * Checks if childElement is a descendant of parentElement (contained in it or a sub element)
   * @param {HTMLElement} parentElement Parent element
   * @param {HTMLElement} childElement Child element, at any level below parent (includes nested shadow roots)
   * @returns {Boolean}
   * @category Query children
   */
  static isDescendant(parentElement, childElement) {
    const defaultResult = parentElement.contains(childElement);
    if (!defaultResult && childElement) {
      const parentRoot = DH.getRootElement(parentElement), childRoot = DH.getRootElement(childElement);
      if (childRoot && parentRoot !== childRoot && childRoot.host) {
        return DH.isDescendant(parentRoot, childRoot.host);
      }
    }
    return defaultResult;
  }
  /**
   * Returns the specified element of the given `event`. If the `event` is an `Element`, it is returned. Otherwise,
   * the `eventName` argument is used to retrieve the desired element property from `event` (this defaults to the
   * `'target'` property).
   * @param {Event|Element} event
   * @param {String} [elementName]
   * @returns {Element}
   */
  static getEventElement(event, elementName = "target") {
    return !event || DH.isElement(event) ? event : event[elementName];
  }
  /**
   * Returns `true` if the provided value is _likely_ a DOM element. If the element can be assured to be from the
   * same document, `instanceof Element` is more reliable.
   * @param {*} value
   * @returns {Boolean}
   */
  static isElement(value2) {
    return (value2 == null ? void 0 : value2.nodeType) === document.ELEMENT_NODE && DH.isNode(value2);
  }
  /**
   * Returns `true` if the provided element is an instance of React Element.
   * @param {*} element
   * @returns {Boolean}
   * @internal
   */
  static isReactElement(element) {
    var _a5, _b2;
    return Boolean((_b2 = (_a5 = globalThis.bryntum) == null ? void 0 : _a5.react) == null ? void 0 : _b2.isReactElement(element));
  }
  /**
   * Handles the content provided by a React component for the widget.
   * @param {Core.widget.Widget} widget Owner widget
   * @param {*} element React element
   * @internal
   */
  static handleReactElement(widget, element) {
    var _a5, _b2;
    return Boolean((_b2 = (_a5 = globalThis.bryntum) == null ? void 0 : _a5.react) == null ? void 0 : _b2.handleReactElement(widget, element));
  }
  /**
   * Handles the React header element by processing JSX content within the widget.
   * @param {Object} column Object containing grid and id properties
   * @param {HTMLElement} headerElement The header element to be processed
   * @param {*} html JSX content to be processed
   * @internal
   */
  static handleReactHeaderElement(column, headerElement, html) {
    var _a5, _b2;
    return Boolean((_b2 = (_a5 = globalThis.bryntum) == null ? void 0 : _a5.react) == null ? void 0 : _b2.handleReactHeaderElement(column, headerElement, html));
  }
  /**
   * Returns `true` if the provided configuration object is valid for Vue processing.
   * @param {Object} config
   * @returns {Boolean}
   * @internal
   */
  static isVueConfig(config) {
    var _a5, _b2;
    return Boolean((_b2 = (_a5 = globalThis.bryntum) == null ? void 0 : _a5.vue) == null ? void 0 : _b2.isVueConfig(config));
  }
  /**
   * Handles the content provided by a Vue component for the widget.
   * @param {String} html The HTML content to be processed
   * @internal
   */
  static handleVueContent(widget, html) {
    var _a5, _b2;
    (_b2 = (_a5 = globalThis.bryntum) == null ? void 0 : _a5.vue) == null ? void 0 : _b2.handleVueContent(widget, html);
  }
  /**
   * Returns `true` if the provided value is _likely_ a DOM node. If the node can be assured to be from the same
   * document, `instanceof Node` is more reliable.
   * @param {*} value
   * @returns {Boolean}
   */
  static isNode(value2) {
    return Boolean(value2) && typeof value2.nodeType === "number" && !isObject2(value2);
  }
  /**
   * Iterates over each result returned from `element.querySelectorAll(selector)`. Can also be called with only two
   * arguments, in which case the first argument is used as selector and document is used as the element.
   * @param {HTMLElement} element Parent element
   * @param {String} selector CSS selector
   * @param {Function} fn Function called for each found element
   * @category Query children
   */
  static forEachSelector(element, selector, fn2) {
    if (typeof element === "string") {
      throw new Error("DomHelper.forEachSelector must provide a root element context (for shadow root scenario)");
    }
    DH.children(element, selector).forEach(fn2);
  }
  /**
   * Iterates over the direct child elements of the specified element.
   * @param {HTMLElement} element Parent element
   * @param {Function} fn Function called for each child element
   * @category Query children
   */
  static forEachChild(element, fn2) {
    Array.from(element.children).forEach(fn2);
  }
  /**
   * Removes each element returned from `element.querySelectorAll(selector)`.
   * @param {HTMLElement} element
   * @param {String} selector
   * @category Query children
   */
  static removeEachSelector(element, selector) {
    DH.forEachSelector(element, selector, (child) => child.remove());
  }
  /**
  * Removes the passed CSS classes from all descendants of the passed element.
  * @param {HTMLElement} element Outermost element
  * @param {...String} classes CSS classes to remove
  */
  static removeClsGlobally(element, ...classes) {
    classes.forEach((cls) => DH.forEachSelector(element, "." + cls, (child) => child.classList.remove(cls)));
  }
  //endregion
  //region Parents, going up...
  static getAncestor(element, possibleAncestorParents, outerElement = null) {
    let found = false, ancestor, parent = element;
    possibleAncestorParents = ArrayHelper.asArray(possibleAncestorParents);
    while (parent = parent.parentElement) {
      if (possibleAncestorParents.includes(parent)) {
        found = true;
        break;
      }
      if (outerElement && parent === outerElement)
        break;
      ancestor = parent;
    }
    if (!found)
      return null;
    return ancestor || element;
  }
  /**
   * Retrieves all parents to the specified element.
   * @param {HTMLElement} element Element
   * @returns {HTMLElement[]} All parent elements, bottom up
   * @category Query parents
   */
  static getParents(element) {
    const parents = [];
    while (element.parentElement) {
      parents.push(element.parentElement);
      element = element.parentElement;
    }
    return parents;
  }
  //endregion
  //region Creation
  /**
   * Converts the passed id to an id valid for usage as id on a DOM element.
   * @param {String} id
   * @returns {String}
   */
  static makeValidId(id2, replaceValue = "") {
    return StringHelper.makeValidDomId(id2, replaceValue);
  }
  /**
   * Creates an Element, accepts a {@link #typedef-DomConfig} object. Example usage:
   *
   * ```javascript
   * DomHelper.createElement({
   *   tag         : 'table', // defaults to 'div'
   *   className   : 'nacho',
   *   html        : 'I am a nacho',
   *   children    : [ { tag: 'tr', ... }, myDomElement ],
   *   parent      : myExistingElement // Or its id
   *   style       : 'font-weight: bold;color: red',
   *   dataset     : { index: 0, size: 10 },
   *   tooltip     : 'Yay!',
   *   ns          : 'http://www.w3.org/1999/xhtml'
   * });
   * ```
   *
   * @param {DomConfig} config Element config object
   * @param {Object} [options] An object specifying creation options. If this is a boolean value, it is
   * understood to be the `returnAll` option.
   * @param {Boolean} [options.ignoreRefs] Pass `true` to ignore element references.
   * @param {Boolean} [options.returnAll] Specify true to return all elements & child elements created as an array.
   * @privateparam {HTMLElement} [options.replaceElement] Pass element which will be replaced with a created one
   * @returns {HTMLElement|HTMLElement[]|Object<String,HTMLElement>} Single element or array of elements `returnAll` was set to `true`.
   * If any elements had a `reference` property, this will be an object containing a reference to
   * all those elements, keyed by the reference name.
   * @category Creation
   */
  static createElement(config = {}, options) {
    var _a5, _b2;
    let returnAll = options, element, i, ignoreChildRefs, ignoreRefOption, ignoreRefs, key, name, value2, refOwner, refs, syncIdField;
    if (typeof returnAll === "boolean") {
      throw new Error("Clean up");
    } else if (options) {
      ignoreRefs = options.ignoreRefs;
      refOwner = options.refOwner;
      refs = options.refs;
      returnAll = options.returnAll;
      syncIdField = options.syncIdField;
      if (ignoreRefs) {
        ignoreChildRefs = true;
        ignoreRefs = ignoreRefs !== "children";
      }
    }
    if (typeof config.parent === "string") {
      config.parent = document.getElementById(config.parent);
    }
    const parent = config.parent || config.nextSibling && config.nextSibling.parentNode, { dataset, html, reference, syncOptions, text } = config;
    if (syncOptions) {
      syncIdField = syncOptions.syncIdField || syncIdField;
      ignoreRefOption = syncOptions.ignoreRefs;
      if (ignoreRefOption) {
        ignoreChildRefs = true;
        ignoreRefs = ignoreRefOption !== "children";
        options = {
          ...options,
          ignoreRefs: true
        };
      }
    }
    if (ignoreRefs) {
      refOwner = null;
    }
    if (config.ns) {
      element = doc.createElementNS(config.ns, config.tag || "svg");
    } else {
      element = doc.createElement(config.tag || "div");
    }
    if (text != null) {
      DH.setInnerText(element, text);
    } else if (html != null) {
      if (html instanceof DocumentFragment) {
        element.appendChild(html);
      } else {
        element.innerHTML = html;
      }
    }
    if (config.tooltip) {
      DH.Widget.attachTooltip(element, config.tooltip);
    }
    if (config.style) {
      DH.applyStyle(element, config.style);
    }
    if (dataset) {
      for (name in dataset) {
        value2 = dataset[name];
        if (value2 != null) {
          element.dataset[name] = value2;
        }
      }
    }
    if (parent) {
      this.addChild(parent, element, config.nextSibling);
    }
    if (refOwner) {
      element.$refOwnerId = refOwner.id;
    }
    if (reference && !ignoreRefs) {
      if (refOwner) {
        element.$reference = reference;
        refOwner.attachRef(reference, element, config);
      } else {
        if (!refs) {
          options = Object.assign({}, options);
          options.refs = refs = {};
        }
        refs[reference] = element;
        element.setAttribute("data-reference", reference);
      }
    }
    const className = config.className || config.class, keys = Object.keys(config);
    if (className) {
      element.setAttribute("class", DomClassList.normalize(className));
    }
    for (i = 0; i < keys.length; ++i) {
      name = keys[i];
      value2 = config[name];
      if ((key = elementCreateExpandos[name]) != null) {
        element[key || name] = value2;
      } else if (!elementCreateProperties[name] && name && value2 != null) {
        element.setAttribute(name, value2);
      }
    }
    if (!config["aria-hidden"] && !("role" in config) && !config.tabIndex && !config.alt && !_DomHelper.isFocusable(element, true) && !element.htmlFor) {
      element.setAttribute("role", "presentation");
    }
    (_b2 = options == null ? void 0 : options.callback) == null ? void 0 : _b2.call(options, {
      action: "newElement",
      domConfig: config,
      targetElement: element,
      syncId: refOwner ? reference : options.syncIdField && ((_a5 = config.dataset) == null ? void 0 : _a5[options.syncIdField])
    });
    if (returnAll === true) {
      options.returnAll = returnAll = [element];
    } else if (Array.isArray(returnAll)) {
      returnAll.push(element);
    }
    if (config.children) {
      if (syncIdField) {
        element.syncIdMap = {};
      }
      ArrayHelper.asArray(config.children).forEach((child) => {
        var _a6, _b3, _c2, _d;
        if (child) {
          if (typeof child === "string") {
            const textNode = document.createTextNode(child);
            if (refOwner) {
              textNode.$refOwnerId = refOwner.id;
            }
            element.appendChild(textNode);
          } else if (this.isReactElement(child)) {
            (_a6 = options == null ? void 0 : options.reactCallback) == null ? void 0 : _a6.call(options, {
              jsx: child,
              domConfig: config,
              targetElement: element
            });
          } else if (isNaN(child.nodeType)) {
            child.parent = element;
            if (!child.ns && config.ns) {
              child.ns = config.ns;
            }
            const childElement = DH.createElement(child, {
              ...options,
              ignoreRefs: (_c2 = (_b3 = config.syncOptions) == null ? void 0 : _b3.ignoreRef) != null ? _c2 : ignoreChildRefs
            }), syncId = (_d = child.dataset) == null ? void 0 : _d[syncIdField];
            if (syncId != null) {
              element.syncIdMap[syncId] = childElement;
            }
            delete child.parent;
          } else {
            element.appendChild(child);
          }
        }
      });
    }
    element.lastDomConfig = config;
    const result = refs || returnAll || element;
    if (config.replaceElement) {
      config.replaceElement.replaceWith(result);
    }
    return result;
  }
  /**
   * Create element(s) from a template (html string). Note that
   * `textNode`s are discarded unless the `raw` option is passed
   * as `true`.
   *
   * If the template has a single root element, then the single element will be returned
   * unless the `array` option is passed as `true`.
   *
   * If there are multiple elements, then an Array will be returned.
   *
   * @param {String} template The HTML string from which to create DOM content
   * @param {Object} [options] An object containing properties to modify how the DOM is created and returned.
   * @param {Boolean} [options.array] `true` to return an array even if there's only one resulting element.
   * @param {Boolean} [options.raw] Return all child nodes, including text nodes.
   * @param {Boolean} [options.fragment] Return a DocumentFragment.
   * @private
   */
  static createElementFromTemplate(template, options = emptyObject3) {
    const { array, raw, fragment } = options;
    let result;
    if (DH.supportsTemplate) {
      (templateElement || (templateElement = doc.createElement("template"))).innerHTML = template;
      result = templateElement.content;
      if (fragment) {
        return result.cloneNode(true);
      }
    } else {
      result = (htmlParser || (htmlParser = new DOMParser())).parseFromString(template, "text/html").body;
      if (fragment) {
        const nodes = result.childNodes;
        result = document.createDocumentFragment();
        while (nodes.length) {
          result.appendChild(nodes[0]);
        }
        return result;
      }
    }
    if (raw) {
      result = result.childNodes;
    } else {
      result = result.children;
    }
    return result.length === 1 && !array ? result[0] : arraySlice.call(result);
  }
  /**
   * Dispatches a MouseEvent of the passed type to the element at the visible centre of the passed element.
   * If the element is not in view, the event is not dispatched.
   * @param {HTMLElement} targetElement The element whose center receives the mouse event.
   * @param {String} [type=contextmenu] The mouse event type to dispatch.
   * @param {Object} [props] Additional properties to set on the event.
   * @returns {MouseEvent} The dispatched event.
   * @internal
   */
  static triggerMouseEvent(targetElement, type = "contextmenu", props = {}) {
    const isInView = this.isInView(targetElement), targetPoint = isInView == null ? void 0 : isInView.center;
    if (isInView) {
      const result = new MouseEvent(type, {
        clientX: targetPoint.x,
        clientY: targetPoint.y,
        offsetX: isInView.width / 2,
        offsetY: isInView.height / 2,
        bubbles: true,
        ...props
      });
      targetElement.dispatchEvent(result);
      return result;
    }
  }
  /**
   * Inserts an `element` at first position in `into`.
   * @param {HTMLElement} into Parent element
   * @param {HTMLElement} element Element to insert, or an element config passed on to createElement()
   * @returns {HTMLElement}
   * @category Creation
   */
  static insertFirst(into, element) {
    if (element && element.nodeType !== ELEMENT_NODE && element.tag) {
      element = DH.createElement(element);
    }
    return into.insertBefore(element, into.firstElementChild);
  }
  /**
   * Inserts an `element` before `beforeElement` in `into`.
   * @param {HTMLElement} into Parent element
   * @param {HTMLElement} element Element to insert, or an element config passed on to createElement()
   * @param {HTMLElement} beforeElement Element before which passed element should be inserted
   * @returns {HTMLElement}
   * @category Creation
   */
  static insertBefore(into, element, beforeElement) {
    if (element && element.nodeType !== ELEMENT_NODE && element.tag) {
      element = DH.createElement(element);
    }
    return beforeElement ? into.insertBefore(element, beforeElement) : DH.insertFirst(into, element);
  }
  static insertAt(parentElement, newElement, index) {
    const siblings = Array.from(parentElement.children);
    if (index >= siblings.length) {
      return DH.append(parentElement, newElement);
    }
    const beforeElement = siblings[index];
    return DH.insertBefore(parentElement, newElement, beforeElement);
  }
  /**
   * Appends element to parentElement.
   * @param {HTMLElement} parentElement Parent element
   * @param {HTMLElement|DomConfig|String} elementOrConfig Element to insert, or an element config passed on to
   * `createElement()`, or an HTML string passed to `createElementFromTemplate()`
   * @returns {HTMLElement}
   * @category Creation
   */
  static append(parentElement, elementOrConfig) {
    if (elementOrConfig.forEach) {
      if (Array.isArray(elementOrConfig)) {
        elementOrConfig = elementOrConfig.map((elementOrConfig2) => elementOrConfigToElement(elementOrConfig2));
      }
      if (parentElement.append) {
        parentElement.append(...elementOrConfig);
      } else {
        const docFrag = document.createDocumentFragment();
        elementOrConfig.forEach(function(child) {
          docFrag.appendChild(child);
        });
        parentElement.appendChild(docFrag);
      }
      return elementOrConfig;
    } else {
      return parentElement.appendChild(elementOrConfigToElement(elementOrConfig));
    }
  }
  //endregion
  //region Get position
  /**
   * Returns the element's `transform translateX` value in pixels.
   * @param {HTMLElement} element
   * @returns {Number} X transform
   * @category Position, get
   */
  static getTranslateX(element) {
    const transformStyle = element.style.transform;
    let matches = pxTtranslateXRe.exec(transformStyle);
    if (matches) {
      return parseFloat(matches[2]);
    } else {
      matches = translateMatrixRe.exec(transformStyle) || translateMatrixRe.exec(DH.getStyleValue(element, "transform"));
      return matches ? parseFloat(matches[1] || matches[3]) : 0;
    }
  }
  /**
   * Returns the element's `transform translateY` value in pixels.
   * @param {HTMLElement} element
   * @returns {Number} Y coordinate
   * @category Position, get
   */
  static getTranslateY(element) {
    const transformStyle = element.style.transform;
    let matches = pxTtranslateYRe.exec(transformStyle);
    if (matches) {
      const y = parseFloat(matches[matches[1] === "Y" ? 2 : 3]);
      return isNaN(y) ? 0 : y;
    } else {
      matches = translateMatrixRe.exec(transformStyle) || translateMatrixRe.exec(DH.getStyleValue(element, "transform"));
      return matches ? parseFloat(matches[2] || matches[4]) : 0;
    }
  }
  /**
   * Gets both X and Y coordinates as an array [x, y]
   * @param {HTMLElement} element
   * @returns {Number[]} [x, y]
   * @category Position, get
   */
  static getTranslateXY(element) {
    return [DH.getTranslateX(element), DH.getTranslateY(element)];
  }
  /**
   * Get elements X offset within a containing element
   * @param {HTMLElement} element
   * @param {HTMLElement} container
   * @returns {Number} X offset
   * @category Position, get
   */
  static getOffsetX(element, container = null) {
    return container ? element.getBoundingClientRect().left - container.getBoundingClientRect().left : element.offsetLeft;
  }
  /**
   * Get elements Y offset within a containing element
   * @param {HTMLElement} element
   * @param {HTMLElement} container
   * @returns {Number} Y offset
   * @category Position, get
   */
  static getOffsetY(element, container = null) {
    return container ? element.getBoundingClientRect().top - container.getBoundingClientRect().top : element.offsetTop;
  }
  /**
   * Gets elements X and Y offset within containing element as an array [x, y]
   * @param {HTMLElement} element
   * @param {HTMLElement} container
   * @returns {Number[]} [x, y]
   * @category Position, get
   */
  static getOffsetXY(element, container = null) {
    return [DH.getOffsetX(element, container), DH.getOffsetY(element, container)];
  }
  /**
   * Get elements X position on page
   * @param {HTMLElement} element
   * @returns {Number}
   * @category Position, get
   */
  static getPageX(element) {
    return element.getBoundingClientRect().left + win.pageXOffset;
  }
  /**
   * Get elements Y position on page
   * @param {HTMLElement} element
   * @returns {Number}
   * @category Position, get
   */
  static getPageY(element) {
    return element.getBoundingClientRect().top + win.pageYOffset;
  }
  /**
   * Returns extremal (min/max) size (height/width) of the element in pixels
   * @param {HTMLElement} element
   * @param {String} style minWidth/minHeight/maxWidth/maxHeight
   * @returns {Number}
   * @internal
   */
  static getExtremalSizePX(element, style) {
    const prop = StringHelper.hyphenate(style), measure = prop.split("-")[1];
    let value2 = DH.getStyleValue(element, prop);
    if (/%/.test(value2)) {
      if (element.parentElement) {
        value2 = parseInt(DH.getStyleValue(element.parentElement, measure), 10);
      } else {
        value2 = NaN;
      }
    } else {
      value2 = parseInt(value2, 10);
    }
    return value2;
  }
  //endregion
  //region Set position
  /**
   * Set element's `scale`.
   * @param {HTMLElement} element
   * @param {Number} scaleX The value by which the element should be scaled in the X axis (0 to 1)
   * @param {Number} [scaleY] The value by which the element should be scaled in the Y axis (0 to 1).
   * Defaults to `scaleX`
   * @category Position, set
   * @internal
   */
  static setScale(element, scaleX, scaleY = scaleX) {
    const t = DH.getStyleValue(element, "transform").split(/,\s*/);
    if (t.length > 1) {
      if (t[0].startsWith("matrix3d")) {
        t[0] = `matrix3d(${scaleX}`;
        t[5] = scaleY;
      } else {
        t[0] = `matrix(${scaleX}`;
        t[3] = scaleY;
      }
      element.style.transform = t.join(",");
    } else {
      element.style.transform = `scale(${scaleX}, ${scaleY})`;
    }
  }
  /**
   * Set element's `X` translation in pixels.
   * @param {HTMLElement} element
   * @param {Number} x The value by which the element should be translated from its default position.
   * @category Position, set
   */
  static setTranslateX(element, x) {
    const t = DH.getStyleValue(element, "transform").split(/,\s*/);
    x = DH.roundPx(x);
    if (t.length > 1) {
      t[t[0].startsWith("matrix3d") ? 12 : 4] = x;
      element.style.transform = t.join(",");
    } else {
      element.style.transform = `translateX(${x}px)`;
    }
  }
  /**
   * Set element's `Y` translation in pixels.
   * @param {HTMLElement} element
   * @param {Number} y  The value by which the element should be translated from its default position.
   * @category Position, set
   */
  static setTranslateY(element, y) {
    const t = DH.getStyleValue(element, "transform").split(/,\s*/);
    y = DH.roundPx(y);
    if (t.length > 1) {
      t[t[0].startsWith("matrix3d") ? 13 : 5] = y;
      element.style.transform = t.join(",") + ")";
    } else {
      element.style.transform = `translateY(${y}px)`;
    }
  }
  /**
   * Set element's style `top`.
   * @param {HTMLElement} element
   * @param {Number|String} y The top position. If numeric, `'px'` is used as the unit.
   * @category Position, set
   */
  static setTop(element, y) {
    DH.setLength(element, "top", y);
  }
  /**
   * Set element's style `left`.
   * @param {HTMLElement} element
   * @param {Number|String} x The top position. If numeric, `'px'` is used as the unit.
   * @category Position, set
   */
  static setLeft(element, x) {
    DH.setLength(element, "left", x);
  }
  static setTopLeft(element, y, x) {
    DH.setLength(element, "top", y);
    DH.setLength(element, "left", x);
  }
  static setTopInsetInlineStart(element, y, x) {
    DH.setLength(element, "top", y);
    DH.setLength(element, "inset-inline-start", x);
  }
  static setRect(element, { x, y, width, height }) {
    DH.setTopInsetInlineStart(element, y, x);
    DH.setLength(element, "width", width);
    DH.setLength(element, "height", height);
  }
  /**
   * Set elements `X` and `Y` translation in pixels.
   * @param {HTMLElement} element
   * @param {Number} [x] The `X translation.
   * @param {Number} [y] The `Y translation.
   * @category Position, set
   */
  static setTranslateXY(element, x, y) {
    if (x == null) {
      return DH.setTranslateY(element, y);
    }
    if (y == null) {
      return DH.setTranslateX(element, x);
    }
    x = DH.roundPx(x);
    y = DH.roundPx(y);
    const t = DH.getStyleValue(element, "transform").split(/,\s*/), is3d = t[0].startsWith("matrix3d");
    if (t.length > 1) {
      t[is3d ? 12 : 4] = x;
      t[is3d ? 13 : 5] = y;
      element.style.transform = t.join(",") + ")";
    } else {
      element.style.transform = `translate(${x}px, ${y}px)`;
    }
  }
  /**
   * Increase `X` translation
   * @param {HTMLElement} element
   * @param {Number} x The number of pixels by which to increase the element's `X` translation.
   * @category Position, set
   */
  static addTranslateX(element, x) {
    DH.setTranslateX(element, DH.getTranslateX(element) + x);
  }
  /**
   * Increase `Y` position
   * @param {HTMLElement} element
   * @param {Number} y The number of pixels by which to increase the element's `Y` translation.
   * @category Position, set
   */
  static addTranslateY(element, y) {
    DH.setTranslateY(element, DH.getTranslateY(element) + y);
  }
  /**
   * Increase X position
   * @param {HTMLElement} element
   * @param {Number} x
   * @category Position, set
   */
  static addLeft(element, x) {
    DH.setLeft(element, DH.getOffsetX(element) + x);
  }
  /**
   * Increase Y position
   * @param {HTMLElement} element
   * @param {Number} y
   * @category Position, set
   */
  static addTop(element, y) {
    DH.setTop(element, DH.getOffsetY(element) + y);
  }
  /**
   * Align the passed element with the passed target according to the align spec.
   * @param {HTMLElement} element The element to align.
   * @param {HTMLElement|Core.helper.util.Rectangle} target The target element or rectangle to align to
   * @param {AlignSpec} [alignSpec] See {@link Core.helper.util.Rectangle#function-alignTo} Defaults to `{ align : 't0-t0' }`
   * @param {Boolean} [round=false] Round the calculated Rectangles (for example if dealing with scrolling which
   * is integer based).
   */
  static alignTo(element, target, alignSpec = t0t0, round2, method = "translate") {
    target = target instanceof Rectangle ? target : Rectangle.from(target, true);
    const elRect = Rectangle.from(element, true);
    if (round2) {
      elRect.roundPx();
      target.roundPx();
    }
    const targetRect = elRect.alignTo(Object.assign(alignSpec, {
      target
    }));
    if (method === "topInset") {
      const elXY = [parseInt(element.style.insetInlineStart || 0, 10), parseInt(element.style.top || 0, 10)];
      DH.setTopInsetInlineStart(element, elXY[1] + targetRect.y - elRect.y, elXY[0] + targetRect.x - elRect.x);
    } else if (method === "topLeft") {
      const elXY = [parseInt(element.style.left || 0, 10), parseInt(element.style.top || 0, 10)];
      DH.setTopLeft(element, elXY[1] + targetRect.y - elRect.y, elXY[0] + targetRect.x - elRect.x);
    } else {
      const elXY = DH.getTranslateXY(element);
      DH.setTranslateXY(element, elXY[0] + targetRect.x - elRect.x, elXY[1] + targetRect.y - elRect.y);
    }
  }
  //endregion
  //region Styles & CSS
  /**
   * Returns a style value or values for the passed element.
   * @param {HTMLElement} element The element to read styles from
   * @param {String|String[]} propName The property or properties to read
   * @param {Boolean} [inline=false] Pass as `true` to read the element's inline style.
   * Note that this could return inaccurate results if CSS rules apply to this element.
   * @returns {String|Object} The value or an object containing the values keyed by the requested property name.
   * @category CSS
   */
  static getStyleValue(element, propName, inline, pseudo) {
    const styles2 = inline ? element.style : element.ownerDocument.defaultView.getComputedStyle(element, pseudo);
    if (Array.isArray(propName)) {
      const result = {};
      for (const prop of propName) {
        result[prop] = styles2.getPropertyValue(StringHelper.hyphenate(prop));
      }
      return result;
    }
    return styles2.getPropertyValue(StringHelper.hyphenate(propName));
  }
  /**
   * Returns an object with the parse style values for the top, right, bottom, and left
   * components of the given edge style.
   *
   * The return value is an object with `top`, `right`, `bottom`, and `left` properties
   * for the respective components of the edge style, as well as `width` (the sum of
   * `left` and `right`) and `height` (the sum of `top` and `bottom`).
   *
   * @param {HTMLElement} element
   * @param {String} edgeStyle The element's desired edge style such as 'padding', 'margin',
   * or 'border'.
   * @param {String} [edges='trbl'] A string with one character codes for each edge. Only
   * those edges will be populated in the returned object. By default, all edges will be
   * populated.
   * @returns {Object}
   */
  static getEdgeSize(element, edgeStyle, edges) {
    const suffix = edgeStyle === "border" ? "-width" : "", ret = {
      raw: {}
    };
    for (const edge of ["top", "right", "bottom", "left"]) {
      if (!edges || edges.includes(edge[0])) {
        ret[edge] = parseFloat(
          ret.raw[edge] = DH.getStyleValue(element, `${edgeStyle}-${edge}${suffix}`)
        );
      }
    }
    ret.width = (ret.left || 0) + (ret.right || 0);
    ret.height = (ret.top || 0) + (ret.bottom || 0);
    return ret;
  }
  /**
   * Splits a style string up into object form. For example `'font-weight:bold;font-size:150%'`
   * would convert to
   *
   * ```javascript
   * {
   *     font-weight : 'bold',
   *     font-size : '150%'
   * }
   * ```
   * @param {String} style A DOM style string
   * @returns {Object} the style declaration in object form.
   */
  static parseStyle(style) {
    if (typeof style === "string") {
      const styles2 = style.split(semicolonRe);
      style = {};
      for (let i = 0, { length } = styles2; i < length; i++) {
        const propVal = styles2[i].split(colonRe);
        style[propVal[0]] = propVal[1];
      }
    }
    return style || {};
  }
  /**
   * Applies specified style to the passed element. Style can be an object or a string.
   * @param {HTMLElement} element Target element
   * @param {String|Object} style Style to apply, 'border: 1px solid black' or { border: '1px solid black' }
   * @param {Boolean} [overwrite] Specify `true` to replace style instead of applying changes
   * @category CSS
   */
  static applyStyle(element, style, overwrite = false) {
    if (typeof style === "string") {
      if (overwrite) {
        if (style.length || element.style.cssText.length) {
          element.style.cssText = style;
        }
      } else {
        element.style.cssText += style;
      }
    } else if (style) {
      if (overwrite) {
        element.style.cssText = "";
      }
      if (style.style && typeof style.style !== "string") {
        style = ObjectHelper.assign({}, style, style.style);
      }
      let key, value2;
      for (key in style) {
        if (!styleIgnoreProperties[key]) {
          [key, value2] = DH.unitize(key, style[key]);
          if (value2 == null) {
            element.style.removeProperty(key);
          } else {
            element.style.setProperty(key, value2);
          }
        }
      }
      if (typeof style.style === "string") {
        element.style.cssText += style.style;
      }
    }
  }
  static getCSSText(style) {
    if (typeof style === "string") {
      return style;
    }
    let cssText = "";
    for (const key in style) {
      if (!styleIgnoreProperties[key]) {
        cssText += `${StringHelper.hyphenate(key)}:${style[key]};`;
      }
    }
    return cssText;
  }
  /**
   * Adds or removes the `classes` contained in the specified object based on the truthy or falsy value of that key.
   *
   * For example:
   * ```javascript
   *  DomHelper.assignClasses(element, {
   *      'b-class-one' : 1   // class will be added
   *      'b-class-two' : 0   // class will be removed
   *  });
   * ```
   * @param {HTMLElement} element
   * @param {Object} classes An object whose truthy keys will be added as classes and whose falsy keys will be removed.
   * @category CSS
   */
  static assignClasses(element, classes) {
    ObjectHelper.forEach(classes, (val, cls) => element.classList.toggle(cls, Boolean(val)));
  }
  /**
   * Toggle multiple classes in elements classList. Helper for toggling multiple classes at once.
   * @param {HTMLElement} element
   * @param {String[]} classes
   * @param {Boolean} [force] Specify true to add classes, false to remove. Leave blank to toggle
   * @category CSS
   */
  static toggleClasses(element, classes, force = null) {
    classes = ArrayHelper.asArray(classes);
    if (force === true) {
      element.classList.add(...classes);
    } else if (force === false) {
      element.classList.remove(...classes);
    } else {
      classes.forEach((cls) => element.classList.toggle(cls));
    }
  }
  /**
   * Adds a CSS class to an element during the specified duration
   * @param {HTMLElement} element Target element
   * @param {String} cls CSS class to add temporarily
   * @param {Number} duration Duration in ms, 0 means cls will not be applied
   * @param {Core.mixin.Delayable|Window} [delayable] The delayable to tie the setTimeout call to
   * @typings delayable -> {typeof Delayable|Window}
   * @privateparam {String} [name] Timer name
   * @category CSS
   */
  static addTemporaryClass(element, cls, duration, delayable = globalThis, name = cls) {
    if (duration > 0) {
      element.classList.add(cls);
      if (delayable === globalThis) {
        delayable.setTimeout(() => element == null ? void 0 : element.classList.remove(cls), duration);
      } else {
        delayable.setTimeout({
          fn: (cls2) => element.classList.remove(cls2),
          delay: duration,
          name,
          args: [cls],
          cancelOutstanding: true
        });
      }
    }
  }
  /**
   * Reads computed style from the element and returns transition duration for a given property in milliseconds
   * @param {HTMLElement} element Target DOM element
   * @param {String} property Animated property name
   * @returns {Number} Duration in ms
   * @internal
   */
  static getPropertyTransitionDuration(element, property) {
    const style = globalThis.getComputedStyle(element), properties = style.transitionProperty.split(", "), durations = style.transitionDuration.split(", "), index = properties.indexOf(StringHelper.hyphenate(property));
    let result;
    if (index !== -1) {
      result = parseFloat(durations[index]) * 1e3;
    }
    return result;
  }
  /**
   * Reads computed style from the element and returns the animation duration for any
   * attached animation in milliseconds
   * @param {HTMLElement} element Target DOM element
   * @returns {Number} Duration in ms
   * @internal
   */
  static getAnimationDuration(element) {
    return parseFloat(DH.getStyleValue(element, "animation-duration")) * 1e3;
  }
  //endregion
  //region Effects
  /**
   * Highlights the passed element or Rectangle according to the theme's highlighting rules.
   * Usually an animated framing effect.
   *
   * The framing effect is achieved by adding the CSS class `b-fx-highlight` which references
   * a `keyframes` animation named `b-fx-highlight-animation`. You may override the animation
   * name referenced, or the animation itself in your own CSS.
   *
   * @param {HTMLElement|Core.helper.util.Rectangle} element The element or Rectangle to highlight.
   */
  static highlight(element, delayable = globalThis) {
    if (element instanceof Rectangle) {
      return element.highlight();
    }
    return new Promise((resolve) => {
      delayable.setTimeout(() => {
        element.classList.add("b-fx-highlight");
        delayable.setTimeout(() => {
          element.classList.remove("b-fx-highlight");
          resolve();
        }, 2e3);
      }, 0);
    });
  }
  //endregion
  //region Measuring / Scrollbar
  /**
   * Measures the scrollbar width using a hidden div. Caches result
   * @member {Number} scrollBarWidth
   * @readonly
   * @static
   */
  static get scrollBarWidth() {
    if (scrollBarWidth === null) {
      const element = scrollBarMeasureElement || (scrollBarMeasureElement = DH.createElement({
        parent: doc.documentElement,
        className: "b-scrollbar-measure-element",
        style: "position:absolute;top:-9999em;height:100px;overflow-y:scroll"
      }));
      if (element.parentNode !== doc.documentElement) {
        doc.documentElement.appendChild(element);
      }
      scrollBarWidth = element.offsetWidth;
      doc.documentElement.style.setProperty("--b-scrollbar-width", `${scrollBarWidth}px`);
    }
    return scrollBarWidth;
  }
  static get scrollBarPadElement() {
    return {
      className: "b-yscroll-pad",
      children: [{
        className: "b-yscroll-pad-sizer"
      }]
    };
  }
  /**
   * Returns the maximum scroll range the browser can correctly manage. This value is browser-specific and typically
   * in the millions of pixels. This answer is not intended to be a precise value but a safe and reliable value to
   * avoid encountering browser limitations.
   * @returns {Number}
   * @internal
   */
  static get scrollLimit() {
    const ct = _DomHelper.createElement({
      parent: document.body,
      style: "position:absolute; left:-999px; top:0; width:5px; height:5px",
      children: [{
        style: "position:absolute; width:1px; height:1px"
      }]
    }), stretcher = ct.firstElementChild;
    let ht = 0, limit = 2e7;
    while (limit > 1e6) {
      stretcher.style.transform = `translateY(${limit}px)`;
      ct.style.lineHeight = `${ht = 1 - ht}`;
      if (ct.scrollHeight >= limit) {
        break;
      }
      limit -= 1e6;
    }
    Object.defineProperty(_DomHelper, "scrollLimit", {
      value: limit
    });
    ct.remove();
    return limit;
  }
  /**
   * Resets DomHelper.scrollBarWidth cache, triggering a new measurement next time it is read
   */
  static resetScrollBarWidth() {
    scrollBarWidth = null;
  }
  /**
   * Measures the text width using a hidden div
   * @param {String} text
   * @param {HTMLElement} sourceElement
   * @returns {Number} width
   * @category Measure
   */
  static measureText(text, sourceElement, useHTML = false, parentElement = void 0) {
    const offScreenDiv = DH.getMeasureElement(sourceElement, parentElement);
    offScreenDiv[useHTML ? "innerHTML" : "innerText"] = text;
    const result = offScreenDiv.clientWidth;
    offScreenDiv.className = "";
    return result;
  }
  /**
   * Measures a relative size, such as a size specified in `em` units for the passed element.
   * @param {String} size The CSS size value to measure.
   * @param {HTMLElement} sourceElement
   * @param {Boolean} [round] Pass true to return exact width, not rounded value
   * @returns {Number} size The size in pixels of the passed relative measurement.
   * @category Measure
   */
  static measureSize(size, sourceElement, round2 = true) {
    var _a5;
    if (!size) {
      return 0;
    }
    if (typeof size === "number") {
      return size;
    }
    if (!size.length) {
      return 0;
    }
    if (/^\d+(px)?$/.test(size)) {
      return parseInt(size);
    }
    if (sourceElement) {
      const percent = (_a5 = size.includes) == null ? void 0 : _a5.call(size, "%"), offScreenDiv = DH.getMeasureElement(sourceElement, percent ? sourceElement.parentNode : void 0), { parentElement } = offScreenDiv;
      offScreenDiv.innerHTML = "";
      percent && (parentElement.style.display = "contents");
      offScreenDiv.style.minWidth = offScreenDiv.style.maxWidth = DH.setLength(size);
      const result = round2 ? offScreenDiv.offsetWidth : offScreenDiv.getBoundingClientRect().width;
      parentElement.style.display = "";
      offScreenDiv.style.minWidth = offScreenDiv.style.maxWidth = offScreenDiv.className = "";
      return result;
    }
    if (/^\d+em$/.test(size)) {
      return parseInt(size) * DEFAULT_FONT_SIZE;
    }
    return isNaN(size) ? 0 : parseInt(size);
  }
  // parentElement allows measurement to happen inside a specific element, allowing scoped css rules to match
  static getMeasureElement(sourceElement, parentElement) {
    parentElement = parentElement || doc.body;
    const sourceElementStyle = win.getComputedStyle(sourceElement), offScreenDiv = parentElement.offScreenDiv = parentElement.offScreenDiv || DH.createElement({
      parent: parentElement,
      style: "position:fixed;top:-10000px;left:-10000px;visibility:hidden;contain:strict",
      className: "b-measure-element",
      children: [{
        style: "white-space:nowrap;display:inline-block;will-change:contents;width:auto;contain:none"
      }]
    }, { returnAll: true })[1];
    fontProps.forEach((prop) => {
      if (offScreenDiv.style[prop] !== sourceElementStyle[prop]) {
        offScreenDiv.style[prop] = sourceElementStyle[prop];
      }
    });
    offScreenDiv.className = sourceElement.className;
    if (offScreenDiv.parentElement.parentElement !== parentElement) {
      parentElement.appendChild(offScreenDiv.parentElement);
    }
    return offScreenDiv;
  }
  /**
   * Strips the tags from a html string, returning text content.
   *
   * ```javascript
   * DomHelper.stripTags('<div class="custom"><b>Bold</b><i>Italic</i></div>'); // -> BoldItalic
   * ```
   *
   * @internal
   * @param {String} htmlString HTML string
   * @returns {String} Text content
   */
  static stripTags(htmlString) {
    const parser = DH.$domParser || (DH.$domParser = new DOMParser()), doc2 = parser.parseFromString(htmlString, "text/html");
    return doc2.body.textContent;
  }
  //endregion
  //region Sync
  /**
   * Sync one source element attributes, children etc. to a target element. Source element can be specified as a html
   * string or an actual HTMLElement.
   *
   * NOTE: This function is superseded by {@link Core/helper/DomSync#function-sync-static DomSync.sync()}, which works
   * with DOM configs. For most usecases, use it instead.
   *
   * @param {String|HTMLElement} sourceElement Source "element" to copy from
   * @param {HTMLElement} targetElement Target element to apply to, can also be specified as part of the config object
   * @returns {HTMLElement} Returns the updated targetElement (which is also updated in place)
   */
  static sync(sourceElement, targetElement) {
    if (typeof sourceElement === "string") {
      if (sourceElement === "") {
        targetElement.innerHTML = "";
        return;
      } else {
        sourceElement = DH.createElementFromTemplate(sourceElement);
      }
    }
    DH.performSync(sourceElement, targetElement);
    return targetElement;
  }
  // Internal helper used for recursive syncing
  static performSync(sourceElement, targetElement) {
    if (sourceElement.outerHTML !== targetElement.outerHTML) {
      DH.syncAttributes(sourceElement, targetElement);
      DH.syncContent(sourceElement, targetElement);
      DH.syncChildren(sourceElement, targetElement);
      return true;
    }
    return false;
  }
  // Attributes as map { attr : value, ... }, either from an html element or from a config
  static getSyncAttributes(element) {
    const attributes = {}, names2 = [];
    for (let i = 0; i < element.attributes.length; i++) {
      const attr = element.attributes[i];
      if (attr.specified) {
        const name = attr.name.toLowerCase();
        attributes[name] = attr.value;
        names2.push(name);
      }
    }
    return { attributes, names: names2 };
  }
  /**
   * Syncs attributes from sourceElement to targetElement.
   * @private
   * @param {HTMLElement} sourceElement
   * @param {HTMLElement} targetElement
   */
  static syncAttributes(sourceElement, targetElement) {
    const {
      attributes: sourceAttributes,
      names: sourceNames
    } = DH.getSyncAttributes(sourceElement), {
      attributes: targetAttributes,
      names: targetNames
    } = DH.getSyncAttributes(targetElement), hasDataset = sourceNames.includes("dataset"), toAdd = sourceNames.filter((attr) => !targetNames.includes(attr)), toRemove = targetNames.filter((attr) => !sourceNames.includes(attr) && (!hasDataset || !attr.startsWith("data-"))), toSync = sourceNames.filter((attr) => targetNames.includes(attr));
    if (toAdd.length > 0) {
      for (let i = 0; i < toAdd.length; i++) {
        const attr = toAdd[i];
        if (attr === "style") {
          DH.applyStyle(targetElement, sourceAttributes.style, true);
        } else if (attr === "dataset") {
          Object.assign(targetElement.dataset, sourceAttributes.dataset);
        } else {
          targetElement.setAttribute(attr, sourceAttributes[attr]);
        }
      }
    }
    if (toRemove.length > 0) {
      for (let i = 0; i < toRemove.length; i++) {
        targetElement.removeAttribute(toRemove[i]);
      }
    }
    if (toSync.length > 0) {
      for (let i = 0; i < toSync.length; i++) {
        const attr = toSync[i];
        if (attr === "style") {
          DH.applyStyle(targetElement, sourceAttributes.style, true);
        } else if (attr === "dataset") {
          Object.assign(targetElement.dataset, sourceAttributes.dataset);
        } else if (attr === "class" && (sourceAttributes.class.isDomClassList || typeof sourceAttributes.class === "object")) {
          let classList;
          if (sourceAttributes.class.isDomClassList) {
            classList = sourceAttributes.class;
          } else {
            classList = new DomClassList(sourceAttributes.class);
          }
          if (!classList.isEqual(targetAttributes.class)) {
            targetElement.setAttribute("class", classList);
          }
        } else if (targetAttributes[attr] !== sourceAttributes[attr]) {
          targetElement.setAttribute(attr, sourceAttributes[attr]);
        }
      }
    }
  }
  /**
   * Sync content (innerText) from sourceElement to targetElement
   * @private
   * @param {HTMLElement} sourceElement
   * @param {HTMLElement} targetElement
   */
  static syncContent(sourceElement, targetElement) {
    if (DH.getChildElementCount(sourceElement) === 0) {
      targetElement.innerText = sourceElement.innerText;
    }
  }
  static setInnerText(targetElement, text) {
    const { firstChild } = targetElement;
    if ((firstChild == null ? void 0 : firstChild.nodeType) === Element.TEXT_NODE) {
      firstChild.data = text;
    } else {
      targetElement.textContent = text;
    }
  }
  /**
   * Sync traversing children
   * @private
   * @param {HTMLElement} sourceElement Source element
   * @param {HTMLElement} targetElement Target element
   */
  static syncChildren(sourceElement, targetElement) {
    const me = this, sourceNodes = arraySlice.call(sourceElement.childNodes), targetNodes = arraySlice.call(targetElement.childNodes);
    while (sourceNodes.length) {
      const sourceNode = sourceNodes.shift(), targetNode = targetNodes.shift();
      if (sourceNode && sourceNode.nodeType !== TEXT_NODE && sourceNode.nodeType !== ELEMENT_NODE) {
        throw new Error(`Source node type ${sourceNode.nodeType} not supported by DomHelper.sync()`);
      }
      if (targetNode && targetNode.nodeType !== TEXT_NODE && targetNode.nodeType !== ELEMENT_NODE) {
        throw new Error(`Target node type ${targetNode.nodeType} not supported by DomHelper.sync()`);
      }
      if (!targetNode) {
        targetElement.appendChild(sourceNode);
      } else {
        if (sourceNode.nodeType === targetNode.nodeType) {
          if (sourceNode.nodeType === TEXT_NODE) {
            targetNode.data = sourceNode.data;
          } else {
            if (sourceNode.tagName === targetNode.tagName) {
              me.performSync(sourceNode, targetNode);
            } else {
              targetElement.insertBefore(sourceNode, targetNode);
              targetNode.remove();
            }
          }
        } else if (sourceNode.nodeType === TEXT_NODE && targetNode.nodeType === ELEMENT_NODE) {
          targetElement.innerText = sourceNode.data.trim();
        } else {
          const logElement = sourceNode.parentElement || sourceNode;
          throw new Error(`Currently no support for transforming nodeType.
${logElement.outerHTML}`);
        }
      }
    }
    targetNodes.forEach((targetNode) => {
      targetNode.remove();
    });
  }
  /**
   * Replaces the passed element's `className` with the class names
   * passed in either Array or String format or Object.
   *
   * This method compares the existing class set with the incoming class set and
   * avoids mutating the element's class name set if possible.
   *
   * This can avoid browser style invalidations.
   * @param {HTMLElement} element The element whose class list to synchronize.
   * @param {String[]|String|Object} newClasses The incoming class names to set on the element.
   * @returns {Boolean} `true` if the DOM class list was changed.
   * @category CSS
   */
  static syncClassList(element, newClasses) {
    const { classList } = element, isString = typeof newClasses === "string", newClsArray = isString ? newClasses.split(whiteSpaceRe2) : DomClassList.normalize(newClasses, "array"), classCount = newClsArray.length;
    let changed = classList.length !== classCount, i;
    for (i = 0; !changed && i < classCount; i++) {
      changed = !classList.contains(newClsArray[i]);
    }
    if (changed) {
      element.className = isString ? newClasses : newClsArray.join(" ");
    }
    return changed;
  }
  /**
   * Applies the key state of the passed object or DomClassList to the passed element.
   *
   * Properties with a falsy value mean that property name is *removed* as a class name.
   *
   * Properties with a truthy value mean that property name is *added* as a class name.
   *
   * This is different from {@link #function-syncClassList-static}. That sets the `className` of the element to the
   * sum of all its truthy keys, regardless of what the pre-existing value of the `className` was, and ignoring falsy
   * keys.
   *
   * This _selectively_ updates the classes in the `className`. If there is a truthy key, the name is added. If there
   * is a falsy key, the name is removed.
   * @param {HTMLElement} element The element to apply the class list to .
   * @param {Object|Core.helper.util.DomClassList} classes The classes to add or remove.
   * @returns {Boolean} `true` if the DOM class list was changed.
   * @category CSS
   */
  static updateClassList(element, classes) {
    const { classList } = element;
    let cls, add, changed = false;
    for (cls in classes) {
      add = Boolean(classes[cls]);
      if (classList.contains(cls) !== add) {
        classList[add ? "add" : "remove"](cls);
        changed = true;
      }
    }
    return changed;
  }
  /**
   * Changes the theme to the passed theme name if possible.
   *
   * Theme names are case insensitive. The `href` used is all lower case.
   *
   * To use this method, the `<link rel="stylesheet">` _must_ use the default,
   * Bryntum-supplied CSS files where the `href` end with `<themeName>.css`, so that
   * it can be found in the document, and switched out for a new link with
   * the a modified `href`. The new `href` will use the same path, just
   * with the `themeName` portion substituted for the new name.
   *
   * If no `<link>` with that name pattern can be found, an error will be thrown.
   *
   * If you use this method, you  must ensure that the theme files are
   * all accessible on your server.
   *
   * Because this is an asynchronous operation, a `Promise` is returned.
   * The theme change event is passed to the success function. If the
   * theme was not changed, because the theme name passed is the current theme,
   * nothing is passed to the success function.
   *
   * The theme change event contains two properties:
   *
   *  - `prev` The previous Theme name.
   *  - `theme` The new Theme name.
   *
   * @param {String} newThemeName the name of the theme that should be applied
   * @privateparam {String} [defaultTheme] Optional, the name of the theme that should be used in case of fail
   * @returns {Promise} A promise who's success callback receives the theme change
   * event if the theme in fact changed. If the theme `href` could not be loaded,
   * the failure callback is called, passing the error event caught.
   * @async
   */
  static setTheme(newThemeName, defaultTheme) {
    newThemeName = newThemeName.toLowerCase();
    const { head } = document, oldThemeName = DH.getThemeInfo(defaultTheme).name.toLowerCase();
    let oldThemeLinks = head.querySelectorAll("[data-bryntum-theme]:not([data-loading])"), loaded = 0;
    if (oldThemeName === newThemeName) {
      return immediatePromise;
    }
    DH.removeEachSelector(head, "#bryntum-theme[data-loading],link[data-bryntum-theme][data-loading]");
    const themeEvent = {
      theme: newThemeName,
      prev: oldThemeName
    };
    function replaceTheme(oldThemeLink, resolve, reject) {
      const newThemeLink = _DomHelper.createElement({
        tag: "link",
        rel: "stylesheet",
        dataset: {
          loading: true,
          bryntumTheme: true
        },
        href: oldThemeLink.href.replace(oldThemeName, newThemeName),
        nextSibling: oldThemeLink
      });
      newThemeLink.addEventListener("load", () => {
        delete newThemeLink.dataset.loading;
        themeInfo = null;
        if (++loaded === oldThemeLinks.length) {
          oldThemeLinks.forEach((link) => link.remove());
          GlobalEvents_default.trigger("theme", themeEvent);
          resolve(themeEvent);
        }
      });
      newThemeLink.addEventListener("error", (e) => {
        delete newThemeLink.dataset.loading;
        reject(e);
      });
    }
    if (oldThemeLinks.length) {
      return new Promise((resolve, reject) => {
        oldThemeLinks.forEach((oldThemeLink, i) => {
          replaceTheme(oldThemeLink, resolve, reject, i === oldThemeLinks.length - 1);
        });
      });
    } else {
      const oldThemeLink = head.querySelector("#bryntum-theme:not([data-loading])") || head.querySelector(`[href*="${oldThemeName}.css"]:not([data-loading])`);
      if (!(oldThemeLink == null ? void 0 : oldThemeLink.href.includes(`${oldThemeName}.css`))) {
        throw new Error(`Theme link for ${oldThemeName} not found`);
      }
      oldThemeLinks = [oldThemeLink];
      return new Promise((resolve, reject) => replaceTheme(oldThemeLink, resolve, reject));
    }
  }
  /**
   * A theme information object about the current theme.
   *
   * @member {ThemeInfo} themeInfo
   * @readonly
   * @static
   */
  static get themeInfo() {
    return _DomHelper.getThemeInfo();
  }
  /**
   * A theme information object about the current theme.
   *
   * Currently, this has only one property:
   *
   *   - `name` The current theme name.
   *
   * @param {String} [defaultTheme] the name of the theme used as backup value in case of failure.
   * @param {HTMLElement} [contextElement] The element for which to find the theme. If using a
   * web component, the theme will be encapsulated in the web component's encapsulated style
   * so a context element is required. If no web components are in use, this may be omitted and
   * `document.body` will be used.
   * @returns {ThemeInfo} Current theme information
   * @internal
   */
  static getThemeInfo(defaultTheme) {
    if (!themeInfo) {
      const testDiv = DH.createElement({
        parent: document.body,
        className: "b-theme-info"
      }), themeData = DH.getStyleValue(testDiv, "content", false, ":before");
      if (themeData) {
        try {
          themeInfo = JSON.parse(themeData.replace(/^["']|["']$|\\/g, ""));
        } catch (e) {
          themeInfo = null;
        }
      }
      themeInfo = themeInfo || (defaultTheme ? { name: defaultTheme } : null);
      testDiv.remove();
    }
    return themeInfo;
  }
  //endregion
  //region Transition
  static async transition({
    element: outerElement,
    selector = "[data-dom-transition]",
    duration,
    action,
    thisObj = this,
    addTransition = {},
    removeTransition = {}
  }) {
    const scrollers = /* @__PURE__ */ new Set(), forceHeight = !outerElement.style.height, beforeElements = Array.from(outerElement.querySelectorAll(selector)), beforeMap = new Map(beforeElements.map((element) => {
      let depth = 0, parent = element.parentElement;
      while (parent && parent !== outerElement) {
        depth++;
        parent = parent.parentElement;
      }
      element.$depth = depth;
      if (element.scrollHeight > element.offsetHeight && getComputedStyle(element).overflow === "auto") {
        element.$scrollTop = element.scrollTop;
        scrollers.add(element);
      }
      const { parentElement } = element, globalBounds = Rectangle.from(element, outerElement), localBounds = Rectangle.from(element, parentElement), style = getComputedStyle(parentElement), borderLeftWidth = parseFloat(style.borderLeftWidth);
      if (borderLeftWidth) {
        globalBounds.left -= borderLeftWidth;
        localBounds.left -= borderLeftWidth;
      }
      return [
        element.id,
        { element, globalBounds, localBounds, depth, parentElement }
      ];
    }));
    if (forceHeight) {
      outerElement.style.height = outerElement.offsetHeight + "px";
    }
    action.call(thisObj);
    const afterElements = Array.from(outerElement.querySelectorAll(selector)), afterMap = new Map(afterElements.map((element) => {
      const globalBounds = Rectangle.from(element, outerElement), localBounds = Rectangle.from(element, element.parentElement), style = globalThis.getComputedStyle(element.parentElement), borderLeftWidth = parseFloat(style.borderLeftWidth);
      if (borderLeftWidth) {
        globalBounds.left -= borderLeftWidth;
        localBounds.left -= borderLeftWidth;
      }
      return [
        element.id,
        { element, globalBounds, localBounds }
      ];
    })), styleProps = ["position", "top", "left", "width", "height", "padding", "margin", "zIndex", "minWidth", "minHeight", "opacity", "overflow"];
    outerElement.classList.add("b-dom-transition-snapshot");
    for (const [id2, before] of beforeMap) {
      const after = afterMap.get(id2);
      if (after) {
        const { element } = after, { style, parentElement } = element, zIndex = parseInt(DH.getStyleValue(element, "zIndex")), {
          globalBounds,
          localBounds,
          depth,
          parentElement: beforeParent
        } = before, parentChanged = beforeParent !== parentElement;
        ObjectHelper.copyProperties(element.$initial = { parentElement }, style, styleProps);
        let bounds;
        if (parentChanged) {
          after.bounds = after.globalBounds;
          bounds = globalBounds;
          outerElement.appendChild(element);
        } else {
          after.bounds = after.localBounds;
          bounds = localBounds;
          beforeParent.appendChild(element);
        }
        let overflow = "hidden";
        if (scrollers.has(element)) {
          element.$scrollPlaceholder = DH.createElement({
            parent: element,
            style: {
              height: element.scrollHeight
            }
          });
          overflow = "auto";
        }
        const targetStyle = {
          position: "absolute",
          top: `${bounds.top}px`,
          left: `${bounds.left}px`,
          width: `${bounds.width}px`,
          height: `${bounds.height}px`,
          minWidth: 0,
          minHeight: 0,
          margin: 0,
          zIndex: depth + (zIndex || 0),
          overflow
        };
        if (element.dataset.domTransition !== "preserve-padding") {
          targetStyle.padding = 0;
        }
        Object.assign(style, targetStyle);
        after.processed = true;
      } else {
        const { element, localBounds: bounds, depth, parentElement } = before;
        element.$initial = { removed: true };
        Object.assign(element.style, {
          position: "absolute",
          top: `${bounds.top}px`,
          left: `${bounds.left}px`,
          width: `${bounds.width}px`,
          height: `${bounds.height}px`,
          minWidth: 0,
          minHeight: 0,
          padding: 0,
          margin: 0,
          zIndex: depth,
          overflow: "hidden"
          // Looks weird with content sticking out if height is transitioned
        });
        parentElement.appendChild(element);
        afterMap.set(id2, { element, bounds, removed: true, processed: true });
        afterElements.push(element);
      }
    }
    for (const [, after] of afterMap) {
      if (!after.processed) {
        const { element } = after, { style, parentElement } = element, bounds = after.bounds = after.localBounds;
        element.classList.add("b-dom-transition-adding");
        ObjectHelper.copyProperties(element.$initial = { parentElement }, style, styleProps);
        Object.assign(style, {
          position: "absolute",
          top: addTransition.top ? 0 : `${bounds.top}px`,
          left: addTransition.left ? 0 : `${bounds.left}px`,
          width: addTransition.width ? 0 : `${bounds.width}px`,
          height: addTransition.height ? 0 : `${bounds.height}px`,
          opacity: addTransition.opacity ? 0 : null,
          zIndex: parentElement.$depth + 1,
          overflow: "hidden"
          // Looks weird with content sticking out if height is transitioned
        });
      }
    }
    for (const element of scrollers) {
      element.scrollTop = element.$scrollTop;
    }
    outerElement.classList.add("b-dom-transition");
    outerElement.firstElementChild.offsetWidth;
    for (const [, { element, bounds: afterBounds, removed }] of afterMap) {
      if (removed) {
        Object.assign(element.style, {
          top: removeTransition.top ? 0 : `${afterBounds.top}px`,
          left: removeTransition.left ? 0 : `${afterBounds.left}px`,
          width: removeTransition.width ? 0 : `${afterBounds.width}px`,
          height: removeTransition.height ? 0 : `${afterBounds.height}px`,
          opacity: removeTransition.opacity ? 0 : element.$initial.opacity
        });
      } else {
        Object.assign(element.style, {
          top: `${afterBounds.top}px`,
          left: `${afterBounds.left}px`,
          width: `${afterBounds.width}px`,
          height: `${afterBounds.height}px`,
          opacity: element.$initial.opacity
        });
      }
    }
    await AsyncHelper.sleep(duration);
    outerElement.classList.remove("b-dom-transition", "b-dom-transition-snapshot");
    for (const element of afterElements) {
      if (element.$initial) {
        if (element.$initial.removed) {
          element.remove();
        } else {
          ObjectHelper.copyProperties(element.style, element.$initial, styleProps);
          if (element.$scrollPlaceholder) {
            element.$scrollPlaceholder.remove();
            delete element.$scrollPlaceholder;
          }
          element.classList.remove("b-dom-transition-adding");
          element.$initial.parentElement.appendChild(element);
        }
      }
    }
    if (forceHeight) {
      outerElement.style.height = "";
    }
    for (const element of scrollers) {
      element.scrollTop = element.$scrollTop;
      delete element.$scrollTop;
    }
  }
  //endregion
  static async loadScript(url) {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = url;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }
  static isNamedColor(color) {
    return color && !/^(#|hsl|rgb|hwb|lab|lch|oklab|oklch)/.test(color);
  }
  static createColorStyle(color) {
    return (color == null ? void 0 : color.length) ? this.isNamedColor(color) ? `var(--cal-color-${color})` : color : "";
  }
  //#region Salesforce hooks
  // Wrap NodeFilter to support salesforce
  static get NodeFilter() {
    return NodeFilter;
  }
  static addChild(parent, child, sibling) {
    parent.insertBefore(child, sibling);
  }
  static cloneStylesIntoShadowRoot(shadowRoot, removeExisting) {
    return new Promise((resolve, reject) => {
      if (removeExisting) {
        shadowRoot.querySelectorAll('style, link[rel="stylesheet"]').forEach((el) => el.remove());
      }
      const links = document.querySelectorAll('link[rel="stylesheet"]');
      let loadCount = 0;
      links.forEach((node) => {
        const clone = node.cloneNode();
        clone.addEventListener("load", () => {
          loadCount += 1;
          if (loadCount === links.length) {
            resolve();
          }
        });
        clone.addEventListener("error", (e) => {
          reject(clone.href);
        });
        shadowRoot.appendChild(clone);
      });
      document.querySelectorAll("style").forEach((node) => {
        const clone = node.cloneNode();
        clone.innerHTML = node.innerHTML;
        shadowRoot.appendChild(clone);
      });
      if (!links.length) {
        resolve();
      }
    });
  }
  //#endregion
};
__publicField(_DomHelper, "_usingKeyboard", false);
/**
 * Set to `false` to not show focus renditions when using keyboard
 * @member {Boolean} enableKeyboardCssModifiers
 * @default true
 */
__publicField(_DomHelper, "enableKeyboardCssModifiers", true);
__publicField(_DomHelper, "$name", "DomHelper");
var DomHelper = _DomHelper;
Helpers_default.register(DomHelper);
var DH = DomHelper;
var clearTouchTimer;
var clearTouchEvent = () => DH.isTouchEvent = false;
var setTouchEvent = () => {
  DH.isTouchEvent = true;
  clearTimeout(clearTouchTimer);
  clearTouchTimer = setTimeout(clearTouchEvent, 400);
};
doc.addEventListener("touchstart", setTouchEvent, true);
doc.addEventListener("touchend", setTouchEvent, true);
DH.canonicalStyles = canonicalStyles;
DH.supportsTemplate = "content" in doc.createElement("template");
DH.elementPropKey = elementPropKey;
DH.numberRe = numberRe;
Node.prototype.closest = function(selector) {
  var _a5;
  return (_a5 = this.parentNode) == null ? void 0 : _a5.closest(selector);
};
globalThis.addEventListener("resize", () => scrollBarWidth = null);
DomHelper._$name = "DomHelper";

// ../Core/lib/Core/GlobalEvents.js
var activeTouches = /* @__PURE__ */ new Map();
var longpressMoveThreshold = 5;
var isFloatingWidget = (w) => w.floating;
var longPressCancelEvents = {
  touchend: 1,
  pointerup: 1
};
var ignoreModifierKeys = {
  Meta: 1,
  Control: 1,
  Alt: 1,
  Shift: 1
};
var onPointerUp = (event) => {
  if ((currentPointerDown == null ? void 0 : currentPointerDown.pointerId) === event.pointerId) {
    currentPointerDown = null;
  }
};
var trackTouches = (event) => {
  const { touches } = event;
  activeTouches.clear();
  if (touches) {
    for (let i = 0; i < touches.length; i++) {
      const touch = touches.item(i);
      activeTouches.set(touch.identifier, [touch, event]);
    }
  }
};
var GlobalEvents = new class GlobalEventsHandler extends Base.mixin(Events_default) {
  suspendFocusEvents() {
    focusEventsSuspended = true;
  }
  resumeFocusEvents() {
    focusEventsSuspended = false;
  }
  setupFocusListenersOnce(rootElement, EventHelper2) {
    if (rootElement && !GlobalEvents.observedElements.has(rootElement)) {
      GlobalEvents.setupFocusListeners(rootElement, EventHelper2);
      GlobalEvents.observedElements.add(rootElement);
    }
  }
  detachFocusListeners(rootElement) {
    if (rootElement.$globalEventsDetacher) {
      rootElement.$globalEventsDetacher();
      rootElement.$globalEventsDetacher = null;
      GlobalEvents.observedElements.delete(rootElement);
    }
  }
  // This is imported by EventHelper and that makes the call to set up the listeners
  // `detach` argument is required to not setup more listeners than we need to. In case of salesforce we include floatroot
  // inside the webcomponent element and thus don't need default listeners on document. In regular webcomponents demo we
  // don't need to do it, because with multiple components on one page that would force us to make more complex lookups.
  setupFocusListeners(element = document, EventHelper2, detach = false) {
    let lastPinchPos, pinchCenter, pinchTarget;
    const listeners = {
      element,
      fromGlobal: true,
      touchstart(touchstart) {
        const { touches: ct } = touchstart;
        trackTouches(touchstart);
        if (ct.length === 2 && ct[0].target.closest(".b-widget") && ct[1].target.closest(".b-widget")) {
          lastPinchPos = [ct[0], ct[1]];
          pinchCenter = [
            (lastPinchPos[0].clientX + lastPinchPos[1].clientX) / 2,
            (lastPinchPos[0].clientY + lastPinchPos[1].clientY) / 2
          ];
          pinchTarget = DomHelper.elementFromPoint(pinchCenter[0], pinchCenter[1]);
          return;
        }
        if (!globalTouchStart && touchstart.changedTouches.length === 1) {
          globalTouchStart = touchstart.changedTouches[0];
          if (!BrowserHelper.isAndroid) {
            const onMoveOrPointerUp = ({ clientX, clientY, type }) => {
              if (longPressCancelEvents[type] || !globalTouchStart || Math.max(Math.abs(clientX - globalTouchStart.clientX), Math.abs(clientY - globalTouchStart.clientY)) > longpressMoveThreshold) {
                contextMenuTouchId = null;
                touchMoveRemover();
                clearTimeout(tapholdTimer);
              }
            }, touchMoveRemover = EventHelper2.on({
              element: document,
              touchmove: onMoveOrPointerUp,
              touchend: onMoveOrPointerUp,
              pointermove: onMoveOrPointerUp,
              pointerup: onMoveOrPointerUp,
              capture: true
            }), tapholdTimer = setTimeout(() => {
              contextMenuTouchId = globalTouchStart.identifier;
              touchMoveRemover();
              touchstart.target.dispatchEvent(new MouseEvent("contextmenu", EventHelper2.copyEvent({}, touchstart)));
            }, EventHelper2.longPressTime);
          }
        } else {
          globalTouchStart = null;
        }
      },
      touchmove: {
        passive: false,
        handler: (touchMove) => {
          const { touches: ct } = touchMove;
          if (ct.length === 2 && lastPinchPos) {
            const pinchPos = [ct[0], ct[1]], delta = EventHelper2.getDistanceBetween(lastPinchPos[0], lastPinchPos[1]) - EventHelper2.getDistanceBetween(pinchPos[0], pinchPos[1]), deltaX = Math.abs(lastPinchPos[0].clientX - lastPinchPos[1].clientX) - Math.abs(pinchPos[0].clientX - pinchPos[1].clientX), deltaY = Math.abs(lastPinchPos[0].clientY - lastPinchPos[1].clientY) - Math.abs(pinchPos[0].clientY - pinchPos[1].clientY);
            if (Math.abs(delta) > 2) {
              const pinch = {
                clientX: pinchCenter[0],
                clientY: pinchCenter[1],
                bubbles: true
              };
              if (GlobalEvents.Widget.convertPinchToMousewheel) {
                touchMove.preventDefault();
                touchMove.stopImmediatePropagation();
                const e13 = new MouseEvent("wheel", { ...pinch, ctrlKey: true });
                e13.deltaY = deltaY;
                e13.deltaX = deltaX;
                pinchTarget.dispatchEvent(e13);
              }
              const e = new MouseEvent("bryntum-pinch", pinch);
              e.deltaY = deltaY;
              e.deltaX = deltaX;
              e.touchMove = touchMove;
              pinchTarget.dispatchEvent(e);
            }
            lastPinchPos = pinchPos;
          }
        }
      },
      // Just this one has to be passive: false so that we are allowed to preventDefault
      // if we are part of a contextmenu longpress emulation. Otherwise the gesture will
      // proceed to cause a mousedown event.
      touchend: {
        passive: false,
        handler: (event) => {
          trackTouches(event);
          if (event.touches.length !== 2) {
            lastPinchPos = null;
          }
          if (globalTouchStart) {
            if (event.changedTouches[0].identifier === contextMenuTouchId) {
              event.stopImmediatePropagation();
              if (event.cancelable !== false) {
                event.preventDefault();
              }
            } else if (event.changedTouches.length === 1 && event.changedTouches[0].identifier === globalTouchStart.identifier) {
              GlobalEvents.trigger("globaltap", { event });
            }
            globalTouchStart = null;
          }
        }
      },
      touchcancel: {
        passive: false,
        handler: trackTouches
      },
      mousedown: {
        handler: (event) => {
          lastInteractionType = "mouse";
          if (!globalTouchStart) {
            GlobalEvents.trigger("globaltap", { event });
          }
          currentMouseDown = event;
          const hasModifierKey = event.ctrlKey || event.altKey || event.shiftKey || event.metaKey;
          if (!currentKeyDown && hasModifierKey) {
            currentKeyDown = new KeyboardEvent("keydown", {
              key: event.ctrlKey ? "Control" : event.shiftKey ? "Shift" : event.altKey ? "Alt" : "Meta",
              ctrlKey: event.ctrlKey,
              altKey: event.altKey,
              shiftKey: event.shiftKey,
              metaKey: event.metaKey
            });
          } else if (currentKeyDown && !hasModifierKey) {
            currentKeyDown = null;
          }
        },
        passive: false
      },
      mouseup: {
        handler: (event) => {
          GlobalEvents.trigger("mouseup", { event });
          currentMouseDown = null;
        },
        passive: false
      },
      pointerdown: {
        passive: false,
        handler: (event) => {
          currentPointerDown = event;
          DomHelper.setUsingKeyboard(element, false);
        }
      },
      pointerup: {
        passive: false,
        handler: onPointerUp
      },
      pointercancel: {
        passive: false,
        handler: onPointerUp
      },
      keydown(ev) {
        const { key, target } = ev;
        lastInteractionType = "key";
        currentKeyDown = ev;
        currentOverElement = target;
        if (!ignoreModifierKeys[key]) {
          if (element.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
            for (const node of element.children) {
              if (node.matches(".b-outer")) {
                DomHelper.setUsingKeyboard(node, true);
              }
            }
          } else {
            DomHelper.setUsingKeyboard(element, true);
          }
          if (key === "Escape") {
            GlobalEvents.Widget.queryAll((w) => w.isTooltip && w.isVisible).forEach((w) => w.hide());
          }
        }
      },
      mouseenter(ev) {
        currentOverElement = ev.target;
      },
      keypress() {
        lastInteractionType = "key";
      },
      keyup() {
        currentKeyDown = null;
      },
      focusin(focusin) {
        var _a5;
        const { Widget: Widget2 } = GlobalEvents, { target } = focusin;
        if ((target == null ? void 0 : target.shadowRoot) || (target == null ? void 0 : target._shadowRoot)) {
          return;
        }
        if (BrowserHelper.supportsPointerEvents && (target == null ? void 0 : target.matches(":focus-visible")) && !bryntum.fromElement(target, (e) => e.isDestroying || e.isDestroyed)) {
          const pointerTarget = target.matches("[class*=event-wrap],[class*=task-wrap]") ? target.firstElementChild : target, rect = Rectangle.from(pointerTarget), center = rect.center;
          pointerTarget.dispatchEvent(new PointerEvent("pointerover", {
            bubbles: true,
            clientX: center.x,
            clientY: center.y,
            pointerType: "focus"
          }));
        }
        if (focusEventsSuspended || target && !target.isConnected) {
          return;
        }
        const fromElement = !focusin.relatedTarget ? null : focusin.relatedTarget instanceof HTMLElement ? focusin.relatedTarget : document.body, toElement = target || document.body, fromWidget = Widget2.fromElement(fromElement), toWidget = Widget2.fromElement(toElement), commonAncestor = DomHelper.getCommonAncestor(fromWidget, toWidget), backwards = Boolean(fromElement && toElement.compareDocumentPosition(fromElement) & 4), topVisibleModal = Widget2.query(isTopVisibleModal);
        let currentFocus = null;
        if (toElement && toElement !== document.body) {
          currentFocus = DomHelper.getActiveElement(toElement);
        } else {
          currentFocus = DomHelper.getActiveElement(document);
        }
        if (topVisibleModal && !topVisibleModal._isRevertingFocus) {
          if (!toWidget || !topVisibleModal.owns(toWidget) && !(topVisibleModal.element.compareDocumentPosition(toWidget.element) & 4 && toWidget.up(isFloatingWidget))) {
            return topVisibleModal.focus();
          }
        }
        let event = createWidgetEvent("focusout", fromElement, target, fromWidget, toWidget, backwards);
        for (let targetWidget = fromWidget, owner; targetWidget && targetWidget !== commonAncestor; targetWidget = owner) {
          owner = targetWidget.owner;
          if (!targetWidget.isDestroying && targetWidget.onFocusOut) {
            targetWidget.onFocusOut(event);
            if (target && currentFocus !== DomHelper.getActiveElement(target)) {
              return;
            }
          }
        }
        if (commonAncestor && target === commonAncestor.element) {
          if (!commonAncestor.isDestroying && DomHelper.getActiveElement(commonAncestor) === toElement && commonAncestor.focusElement && commonAncestor.focusElement !== commonAncestor.element) {
            if (!commonAncestor.element.contains(currentFocus) || commonAncestor.focusDescendant) {
              commonAncestor.setTimeout(() => {
                var _a6;
                return (_a6 = commonAncestor.focus) == null ? void 0 : _a6.call(commonAncestor);
              }, 0);
            }
          }
        } else {
          event = createWidgetEvent("focusin", toElement, fromElement, fromWidget, toWidget, backwards);
          for (let targetWidget = toWidget; targetWidget && targetWidget !== commonAncestor; targetWidget = targetWidget.owner) {
            if (!targetWidget.isDestroying) {
              (_a5 = targetWidget.onFocusIn) == null ? void 0 : _a5.call(targetWidget, event);
            }
          }
        }
        const commonAncestorEl = DomHelper.getCommonAncestor((fromElement == null ? void 0 : fromElement.nodeType) === Element.ELEMENT_NODE ? fromElement : null, toElement) || toElement.parentNode;
        if (commonAncestorEl) {
          event = createWidgetEvent("focusmove", toElement, fromElement, fromWidget, toWidget, backwards, { bubbles: true });
          commonAncestorEl.dispatchEvent(event);
        }
      },
      focusout(focusout) {
        if (focusEventsSuspended) {
          return;
        }
        if (!focusout.relatedTarget || !GlobalEvents.Widget.fromElement(focusout.relatedTarget)) {
          const target = focusout.relatedTarget && focusout.relatedTarget instanceof HTMLElement ? focusout.relatedTarget : null;
          listeners.focusin({
            target,
            relatedTarget: focusout.target
          });
          currentKeyDown = currentMouseDown = null;
        }
      },
      // This will clear keydown and mousedown status on window blur
      blur: {
        element: globalThis,
        handler(event) {
          if (event.target === globalThis) {
            currentKeyDown = null;
            currentMouseDown = null;
          }
        }
      },
      capture: true,
      passive: true
    };
    detach && (detacher == null ? void 0 : detacher());
    detacher = this.detachEvents = element.$globalEventsDetacher = EventHelper2.on(listeners);
  }
  get activeTouches() {
    return Array.from(activeTouches.values());
  }
  get hasActiveTouches() {
    return activeTouches.size > 0;
  }
  get lastInteractionType() {
    return lastInteractionType;
  }
  get shiftKeyDown() {
    return Boolean(currentKeyDown == null ? void 0 : currentKeyDown.shiftKey);
  }
  get ctrlKeyDown() {
    return Boolean((currentKeyDown == null ? void 0 : currentKeyDown.ctrlKey) || (currentKeyDown == null ? void 0 : currentKeyDown.metaKey));
  }
  get altKeyDown() {
    return Boolean(currentKeyDown == null ? void 0 : currentKeyDown.altKey);
  }
  get currentOverElement() {
    return currentOverElement;
  }
  isKeyDown(key) {
    return !key ? Boolean(currentKeyDown) : (currentKeyDown == null ? void 0 : currentKeyDown.key) === key || currentKeyDown[(key == null ? void 0 : key.toLowerCase()) + "Key"] === true;
  }
  isMouseDown(button = 0) {
    return (currentMouseDown == null ? void 0 : currentMouseDown.button) === button;
  }
  get currentMouseDown() {
    return currentMouseDown;
  }
  set currentMouseDown(m) {
    currentMouseDown = m;
  }
  get currentPointerDown() {
    return currentPointerDown;
  }
  set currentPointerDown(p) {
    currentPointerDown = p;
  }
  get currentTouch() {
    return globalTouchStart;
  }
  set currentTouch(t) {
    globalTouchStart = t;
  }
  get currentKeyDown() {
    return currentKeyDown;
  }
  set currentKeyDown(k) {
    currentKeyDown = k;
  }
}();
var isTopVisibleModal = (w) => w.isTopModal;
GlobalEvents.observedElements = /* @__PURE__ */ new Set();
var globalTouchStart;
var contextMenuTouchId;
var focusEventsSuspended = false;
var lastInteractionType;
var currentKeyDown;
var currentMouseDown;
var currentPointerDown;
var currentOverElement;
var detacher;
function createWidgetEvent(eventName, target, relatedTarget, fromWidget, toWidget, backwards, options) {
  const result = new CustomEvent(eventName, options);
  Object.defineProperty(result, "_target", {
    get() {
      return target;
    }
  });
  Object.defineProperty(result, "relatedTarget", {
    get() {
      return relatedTarget;
    }
  });
  result.fromWidget = fromWidget;
  result.toWidget = toWidget;
  result.backwards = backwards;
  return result;
}
var GlobalEvents_default = GlobalEvents;

// ../Core/lib/Core/helper/AjaxHelper.js
var paramValueRegExp = /^(\w+)=(.*)$/;
var parseParams = function(paramString) {
  const result = {}, params = paramString.split("&");
  for (const nameValuePair of params) {
    const [match, name, value2] = paramValueRegExp.exec(nameValuePair), decodedName = decodeURIComponent(name), decodedValue = decodeURIComponent(value2);
    if (match) {
      let paramValue = result[decodedName];
      if (paramValue) {
        if (!Array.isArray(paramValue)) {
          paramValue = result[decodedName] = [paramValue];
        }
        paramValue.push(decodedValue);
      } else {
        result[decodedName] = decodedValue;
      }
    }
  }
  return result;
};
var _AjaxHelper = class _AjaxHelper {
  /**
   * Make a request (using GET) to the specified url.
   * @param {String} url URL to `GET` from
   * @param {FetchOptions} [options] The options for the `fetch` API
   * @returns {Promise} The fetch Promise, which can be aborted by calling a special `abort` method
   * @async
   */
  static get(url, options) {
    return this.fetch(url, options);
  }
  /**
   * POST data to the specified URL.
   * @param {String} url URL to `POST` to
   * @param {String|Object|FormData} payload The data to post. If an object is supplied, it will be stringified
   * @param {FetchOptions} [options] The options for the `fetch` API
   * @returns {Promise} The fetch Promise, which can be aborted by calling a special `abort` method
   * @async
   */
  static post(url, payload, options = {}) {
    options = Objects.assign({}, options);
    if (!(payload instanceof FormData) && !(typeof payload === "string")) {
      payload = JSON.stringify(payload);
      options.headers = options.headers || {};
      options.headers["Content-Type"] = options.headers["Content-Type"] || "application/json";
    }
    return this.fetch(url, Object.assign({
      method: "POST",
      body: payload
    }, options));
  }
  /**
   * Fetch the specified resource using the `fetch` API.
   * @param {String} url URL to fetch from
   * @param {FetchOptions} [options] The options for the `fetch` API
   * @returns {Promise} The fetch Promise, which can be aborted by calling a special `abort` method
   * @async
   */
  static fetch(url, options) {
    let controller;
    options = Objects.merge({}, _AjaxHelper.DEFAULT_FETCH_OPTIONS, options);
    if (typeof AbortController !== "undefined") {
      controller = options.abortController = new AbortController();
      options.signal = controller.signal;
    }
    if (options.queryParams) {
      const params = Object.entries(options.queryParams);
      if (params.length) {
        let paramsAdded = false;
        if (options.headers && options.addQueryParamsToBody === true) {
          const contentType = new Headers(options.headers).get("Content-Type");
          let bodyClass;
          switch (contentType) {
            case "application/x-www-form-urlencoded":
              bodyClass = URLSearchParams;
              break;
            case "multipart/form-data":
              bodyClass = FormData;
              break;
          }
          if (bodyClass) {
            const body = options.body || (options.body = new bodyClass());
            if (body instanceof bodyClass) {
              params.forEach(([key, value2]) => body.set(key, value2));
              paramsAdded = true;
            }
          }
        }
        if (!paramsAdded) {
          url += (url.includes("?") ? "&" : "?") + params.map(
            ([param, value2]) => `${param}=${encodeURIComponent(value2)}`
          ).join("&");
        }
      }
    }
    const promise = new Promise((resolve, reject) => {
      fetch(url, options).then(
        (response) => {
          if (options.parseJson) {
            response.json().then((json) => {
              response.parsedJson = json;
              resolve(response);
            }).catch((error) => {
              response.parsedJson = null;
              response.error = error;
              reject(response);
            });
          } else {
            resolve(response);
          }
        }
      ).catch((error) => {
        error.stack = promise.stack;
        reject(error);
      });
    });
    promise.stack = new Error().stack;
    promise.abort = function() {
      controller == null ? void 0 : controller.abort(new DOMException("User aborted request", "AbortError"));
      this.aborted = true;
    };
    return promise;
  }
  /**
   * Registers the passed URL to return the passed mocked up Fetch Response object to the
   * AjaxHelper's promise resolve function.
   * @param {String} url The url to return mock data for
   * @param {Object|Function} response A mocked up Fetch Response object which must contain
   * at least a `responseText` property, or a function to which the `url` and a `params` object
   * and the `Fetch` `options` object is passed which returns that.
   * @param {String} response.responseText The data to return.
   * @param {Boolean} [response.synchronous] resolve the Promise immediately
   * @param {Number} [response.delay=100] resolve the Promise after this number of milliseconds.
   */
  static mockUrl(url, response) {
    const me = this;
    (me.mockAjaxMap || (me.mockAjaxMap = {}))[url] = response;
    if (!_AjaxHelper.originalFetch) {
      _AjaxHelper.originalFetch = _AjaxHelper.fetch;
      _AjaxHelper.fetch = me.mockAjaxFetch.bind(me);
    }
  }
  static async mockAjaxFetch(url, options) {
    const urlAndParams = url.split("?");
    let result = this.mockAjaxMap[urlAndParams[0]], parsedJson = null;
    if (result) {
      if (typeof result === "function") {
        result = await result(urlAndParams[0], urlAndParams[1] && parseParams(urlAndParams[1]), options);
      }
      try {
        parsedJson = (options == null ? void 0 : options.parseJson) && JSON.parse(result.responseText);
      } catch (error) {
        parsedJson = null;
        result.error = error;
      }
      result = Object.assign({
        status: 200,
        ok: true,
        headers: new Headers(),
        statusText: "OK",
        url,
        parsedJson,
        text: () => new Promise((resolve) => {
          resolve(result.responseText);
        }),
        json: () => new Promise((resolve) => {
          resolve(parsedJson);
        })
      }, result);
      return new Promise(function(resolve, reject) {
        if (result.synchronous) {
          resolve(result);
        } else {
          setTimeout(function() {
            resolve(result);
          }, "delay" in result ? result.delay : 100);
        }
      });
    } else {
      return _AjaxHelper.originalFetch(url, options);
    }
  }
};
/**
 * Sets default options for {@link #function-fetch-static AjaxHelper#fetch()} calls. Please see
 * {@link #typedef-FetchOptions} and
 * [fetch API](https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch) for details.
 *
 * ```javascript
 * // default content-type for all requests will be "application/json"
 * AjaxHelper.DEFAULT_FETCH_OPTIONS = {
 *     headers : {
 *         'content-type' : 'application/json'
 *     }
 * };
 * ```
 * @member {FetchOptions} DEFAULT_FETCH_OPTIONS
 * @static
 */
__publicField(_AjaxHelper, "DEFAULT_FETCH_OPTIONS", {});
var AjaxHelper = _AjaxHelper;
AjaxHelper._$name = "AjaxHelper";

// ../Core/lib/Core/localization/LocaleHelper.js
var _LocaleHelper = class _LocaleHelper {
  /**
   * Merges all properties of provided locale objects into new locale object.
   * Locales are merged in order they provided and locales which go later replace
   * same properties of previous locales.
   * @param {...Object} locales Locales to merge
   * @returns {Object} Merged locale
   */
  static mergeLocales(...locales) {
    const result = {};
    locales.forEach((locale6) => {
      Object.keys(locale6).forEach((key) => {
        if (typeof locale6[key] === "object") {
          result[key] = { ...result[key], ...locale6[key] };
        } else {
          result[key] = locale6[key];
        }
      });
    });
    return result;
  }
  /**
   * Removes all properties from `locale` that are present in the provided `toTrim`.
   * @param {Object} locale Locale to process
   * @param {Object} toTrim Object enumerating properties that should be removed.
   * When `false` throws exceptions in such cases.
   */
  static trimLocale(locale6, toTrim) {
    const remove = (key, subKey) => {
      if (locale6[key]) {
        if (subKey) {
          if (locale6[key][subKey]) {
            delete locale6[key][subKey];
          }
        } else {
          delete locale6[key];
        }
      }
    };
    Object.keys(toTrim).forEach((key) => {
      if (Object.keys(toTrim[key]).length > 0) {
        Object.keys(toTrim[key]).forEach((subKey) => remove(key, subKey));
      } else {
        remove(key);
      }
    });
  }
  /**
   * Normalizes locale object to {@link Locale} type.
   *
   * Supported configs:
   *
   * ```javascript
   * LocaleHelper.normalizeLocale({
   *     localeName : 'En',
   *     localeDesc : 'English (US)',
   *     localeCode : 'en-US',
   *     ... (localization key:value pairs)
   * });
   * ```
   *
   * and for backward compatibility
   *
   * ```javascript
   * LocaleHelper.normalizeLocale('En', {
   *     name : 'En',
   *     desc : 'English (US)',
   *     code : 'en-US',
   *     locale : {
   *         ... (localization key:value pairs)
   *     }
   * });
   * ```
   * @param {String|Object} nameOrConfig String name of locale or locale object
   * @param {Object} [config] Locale object
   * @returns {Locale} Locale object
   * @internal
   */
  static normalizeLocale(nameOrConfig, config) {
    if (!nameOrConfig) {
      throw new Error(`"nameOrConfig" parameter can not be empty`);
    }
    if (typeof nameOrConfig === "string") {
      if (!config) {
        throw new Error(`"config" parameter can not be empty`);
      }
      if (config.locale) {
        config.name = nameOrConfig || config.name;
      } else {
        config.localeName = nameOrConfig;
      }
    } else {
      config = nameOrConfig;
    }
    let locale6 = {};
    if (config.name || config.locale) {
      locale6 = Object.assign({
        localeName: config.name
      }, config.locale);
      config.desc && (locale6.localeDesc = config.desc);
      config.code && (locale6.localeCode = config.code);
      config.path && (locale6.localePath = config.path);
    } else {
      if (!config.localeName) {
        throw new Error(`"config" parameter doesn't have "localeName" property`);
      }
      locale6 = Object.assign({}, config);
    }
    for (const key of ["name", "desc", "code", "path"]) {
      if (locale6[key]) {
        delete locale6[key];
      }
    }
    if (!locale6.localeName) {
      throw new Error(`Locale name can not be empty`);
    }
    return locale6;
  }
  /**
   * Get/set currently published locales.
   * Returns an object with locales.
   *
   * Example:
   *
   * ```javascript
   * const englishLocale = LocaleHelper.locales.En;
   * ```
   *
   * `englishLocale` contains {@link Locale} object.
   *
   * @readonly
   * @member {Locales} locales
   * @static
   */
  static get locales() {
    return _LocaleHelper.global.bryntum.locales || {};
  }
  static set locales(locales) {
    _LocaleHelper.global.bryntum.locales = locales;
  }
  /**
   * Get/set current locale name. Defaults to "En"
   * @member {String} localeName
   * @static
   */
  static get localeName() {
    return _LocaleHelper.global.bryntum.locale || "En";
  }
  static set localeName(localeName) {
    _LocaleHelper.global.bryntum.locale = localeName || _LocaleHelper.localeName;
  }
  /**
   * Get current locale config specified by {@link #property-localeName-static}.
   * If no current locale specified, returns default `En` locale or first published locale
   * or empty locale object if no published locales found.
   * @readonly
   * @member {Locales} locale
   * @static
   */
  static get locale() {
    return _LocaleHelper.localeName && this.locales[_LocaleHelper.localeName] || this.locales.En || Object.values(this.locales)[0] || { localeName: "", localeDesc: "", localeCoode: "" };
  }
  /**
   * Publishes a locale to make it available for applying.
   * Published locales are available in {@link #property-locales-static}.
   *
   * Recommended usage:
   *
   * ```javascript
   * LocaleHelper.publishLocale({
   *     localeName : 'En',
   *     localeDesc : 'English (US)',
   *     localeCode : 'en-US',
   *     ... (localization key:value pairs)
   * });
   * ```
   *
   * for backward compatibility (prior to `5.3.0` version):
   *
   * ```javascript
   * LocaleHelper.publishLocale('En', {
   *     name : 'En',
   *     desc : 'English (US)',
   *     code : 'en-US',
   *     locale : {
   *         ... (localization key:value pairs)
   *     }
   * });
   * ```
   *
   * Publishing a locale will automatically merge it's localization keys with existing locale matching by locale name,
   * replacing existing one with new. To replace existing locale entirely pass `true` to optional `config` parameter.
   *
   * Example:
   *
   * ```javascript
   * LocaleHelper.publishLocale({
   *     localeName : 'En',
   *     localeDesc : 'English (US)',
   *     localeCode : 'en-US',
   *     ... (localization key:value pairs)
   * }, true);
   * ```
   *
   * @param {String|Locale} nameOrConfig String name of locale (for example `En` or `SvSE`) or locale object
   * @param {Locale|Boolean} [config] Locale object.
   * Not used if locale object is passed as first method parameter.
   * Path `true` value and locale object as first method parameter to publish locale without merging with existing one.
   * @returns {Locale} Locale object
   */
  static publishLocale(nameOrConfig, config) {
    const { locales } = _LocaleHelper.global.bryntum, locale6 = _LocaleHelper.normalizeLocale(nameOrConfig, config), { localeName } = locale6;
    if (!locales[localeName] || config === true) {
      locales[localeName] = locale6;
    } else {
      locales[localeName] = this.mergeLocales(locales[localeName] || {}, locale6 || {});
    }
    return locales[localeName];
  }
  static get global() {
    return globalThis;
  }
};
__publicField(_LocaleHelper, "skipLocaleIntegrityCheck", false);
var LocaleHelper = _LocaleHelper;
LocaleHelper.global.bryntum = LocaleHelper.global.bryntum || {};
LocaleHelper.global.bryntum.locales = LocaleHelper.global.bryntum.locales || {};
LocaleHelper._$name = "LocaleHelper";

// ../Core/lib/Core/localization/LocaleManager.js
var LocaleManager = class extends Events_default(Base) {
  static get defaultConfig() {
    return {
      // Enable strict locale checking by default for tests
      throwOnMissingLocale: VersionHelper.isTestEnv
    };
  }
  construct(...args) {
    var _a5;
    const me = this;
    super.construct(...args);
    if (BrowserHelper.isBrowserEnv) {
      const scriptTag = document.querySelector("script[data-default-locale]");
      if (scriptTag) {
        me.applyLocale(scriptTag.dataset.defaultLocale);
      } else if ((_a5 = me.locale) == null ? void 0 : _a5.localeName) {
        me.applyLocale(me.locale.localeName);
      }
    }
  }
  /**
   * Get/set currently registered locales.
   * Alias for {@link Core.localization.LocaleHelper#property-locales-static LocaleHelper.locales}.
   * @readonly
   * @member {Locales} locales
   */
  get locales() {
    return LocaleHelper.locales;
  }
  set locales(locales) {
    LocaleHelper.locales = locales;
  }
  /**
   * Get/set currently used locale.
   * Setter calls {@link #function-applyLocale}.
   * @member {Locales} locale
   * @accepts {String|Locale}
   */
  set locale(nameOrConfig) {
    this.applyLocale(nameOrConfig);
  }
  get locale() {
    return LocaleHelper.locale;
  }
  get localeCode() {
    var _a5, _b2;
    return (_b2 = (_a5 = this.locale) == null ? void 0 : _a5.localeCode) != null ? _b2 : "en-US";
  }
  /**
   * Applies a locale by string name or publishes new locale configuration with
   * {@link Core.localization.LocaleHelper#function-publishLocale-static} and applies it.
   * If locale is specified by string name, like 'En', it must be published before applying it.
   *
   * @param {String|Locale} nameOrConfig String name of locale (for example `En` or `SvSE`) or locale object
   * @param {Locale|Boolean} [config] Locale object. Pass `true` to reapply locale which is passed as first method parameter.
   * @returns {Locale|Promise} locale object or Promise which resolves with locale object after it was loaded
   * @fires locale
   * @async
   * @function applyLocale
   */
  applyLocale(nameOrConfig, config, ignoreError = false) {
    const me = this;
    let localeConfig;
    if (typeof nameOrConfig === "string") {
      if (typeof config !== "object") {
        localeConfig = me.locales[nameOrConfig];
        if (!localeConfig) {
          if (ignoreError) {
            return true;
          }
          throw new Error(`Locale "${nameOrConfig}" is not published. Publish with LocaleHelper.publishLocale() before applying.`);
        }
      } else {
        localeConfig = LocaleHelper.publishLocale(nameOrConfig, config);
      }
    } else {
      localeConfig = LocaleHelper.publishLocale(nameOrConfig);
    }
    if (me.locale.localeName && me.locale.localeName === localeConfig.localeName && config !== true) {
      return me.locale;
    }
    localeConfig.oldLocaleName = me.locale.localeName;
    localeConfig.oldLocaleCode = me.locale.localeCode;
    LocaleHelper.localeName = localeConfig.localeName;
    const triggerLocaleEvent = () => {
      me.trigger("locale", localeConfig);
    };
    if (localeConfig.localePath) {
      return new Promise((resolve, reject) => {
        me.loadLocale(localeConfig.localePath).then((response) => {
          response.text().then((text) => {
            const parseLocale = new Function(text);
            parseLocale();
            if (BrowserHelper.isBrowserEnv) {
              localeConfig = me.locales[localeConfig.localeName];
              if (localeConfig) {
                delete localeConfig.localePath;
              }
            }
            triggerLocaleEvent();
            resolve(localeConfig);
          });
        }).catch((response) => reject(response));
      });
    }
    triggerLocaleEvent();
    return localeConfig;
  }
  /**
   * Loads a locale using AjaxHelper {@link Core.helper.AjaxHelper#function-get-static} request.
   * @private
   * @param {String} path Path to locale file
   * @async
   */
  loadLocale(path) {
    return AjaxHelper.get(path);
  }
  /**
   * Specifies if {@link Core.localization.Localizable#function-L-static Localizable.L()} function would throw error if no localization found at runtime.
   *
   * @member {Boolean} throwOnMissingLocale
   * @default false
   */
  set throwOnMissingLocale(value2) {
    this._throwOnMissingLocale = value2;
  }
  get throwOnMissingLocale() {
    return this._throwOnMissingLocale;
  }
};
var LocaleManagerSingleton = new LocaleManager();
var LocaleManager_default = LocaleManagerSingleton;

// ../Core/lib/Core/localization/Localizable.js
var ObjectProto = Object.getPrototypeOf(Object);
var localeRe = /L{.*?}/g;
var capturelocaleRe = /L{(.*?)}/g;
var classMatchRe = /((.*?)\.)?(.+)/g;
var escape2 = (txt) => txt.replace(/{(\d+)}/gm, "[[$1]]");
var unescape = (txt) => txt.replace(/\[\[(\d+)]]/gm, "{$1}");
var emptyObject4 = Object.freeze(/* @__PURE__ */ Object.create(null));
var Localizable_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    static get configurable() {
      return {
        /**
         * A class translations of which are used for translating this entity.
         * This is often used when translations of an item are defined on its container class.
         * For example:
         *
         * ```javascript
         * // Toolbar class that has some predefined items
         * class MyToolbar extends Toolbar {
         *
         *     static $name = 'MyToolbar';
         *
         *     static get defaultConfig() {
         *         return {
         *             // this specifies default configs for the items
         *             defaults : {
         *                 // will tell items to use the toolbar locale
         *                 localeClass : this
         *             },
         *
         *             items : {
         *                 // The toolbar has 2 buttons and translation for their texts will be searched in
         *                 // the toolbar locales
         *                 agree : { text : 'Agree' },
         *                 disagree : { text : 'Disagree' }
         *             }
         *         };
         *     }
         *
         *    ...
         * }
         * ```
         * So if one makes a locale for the `MyToolbar` class that will include `Agree` and `Disagree` string translations:
         * ```javascript
         *     ...
         *     MyToolbar : {
         *         Agree    : 'Yes, I agree',
         *         Disagree : 'No, I do not agree'
         *     }
         * ```
         * They will be used for the toolbar buttons and the button captions will say `Yes, I agree` and `No, I do not agree`.
         *
         * @config {Core.Base}
         * @typings {typeof Base}
         * @category Misc
         * @advanced
         */
        localeClass: null,
        /**
         * Set to `false` to disable localization of this object.
         * @config {Boolean}
         * @default true
         * @category Misc
         * @advanced
         */
        localizable: null,
        /**
         * List of properties which values should be translated automatically upon a locale applying.
         * In case there is a need to localize not typical value (not a String value or a field with re-defined setter/getter),
         * you could use 'localeKey' meta configuration.
         * Example:
         * ```javascript
         *  static get configurable() {
         *     return {
         *          localizableProperties : ['width'],
         *
         *          width : {
         *              value   : '54em', // default value here
         *              $config : {
         *                  localeKey : 'L{editorWidth}' // name of the property that will be used in localization file
         *              }
         *          }
         *      };
         *  }
         * ```
         * @config {String[]}
         * @category Misc
         * @advanced
         */
        localizableProperties: {
          value: [],
          $config: {
            merge: "distinct"
          }
        }
      };
    }
    static clsName(cls) {
      var _a6, _b2;
      return typeof cls === "string" ? cls : cls === ObjectProto ? "Object" : cls.$$name || cls.name || ((_a6 = cls.prototype) == null ? void 0 : _a6.$$name) || ((_b2 = cls.prototype) == null ? void 0 : _b2.name);
    }
    static parseLocaleString(text) {
      const matches = [];
      let m;
      if (text == null ? void 0 : text.includes("L{")) {
        text = escape2(text);
        capturelocaleRe.lastIndex = 0;
        while ((m = capturelocaleRe.exec(text)) != null) {
          classMatchRe.lastIndex = 0;
          const classMatch = classMatchRe.exec(m[1]);
          matches.push({
            match: unescape(m[0]),
            localeKey: unescape(classMatch[3]),
            localeClass: classMatch[2]
          });
        }
      }
      return matches.length > 0 ? matches : [{
        match: text,
        localeKey: text,
        localeClass: void 0
      }];
    }
    construct(config = {}, ...args) {
      super.construct(config, ...args);
      LocaleManager_default.ion({ locale: "updateLocalization", thisObj: this });
      this.updateLocalization();
    }
    get localeClass() {
      return this._localeClass || null;
    }
    localizeProperty(property) {
      var _a6, _b2, _c2;
      const me = this, currentValue = Objects.getPath(me, property), localeKey = ((_a6 = me.$meta.configs[property]) == null ? void 0 : _a6.localeKey) || ((_c2 = (_b2 = me.fieldMap) == null ? void 0 : _b2[property]) == null ? void 0 : _c2.defaultValue);
      let localizedValue;
      if (localeKey) {
        localizedValue = _a5.localize(localeKey, me, me.localeClass || me);
        if (localizedValue && !(property in (me.initialConfig || emptyObject4)) && !(property in (me.originalData || emptyObject4))) {
          Objects.setPath(me.isColumn ? me.data : me, property, localizedValue);
        } else {
          return currentValue;
        }
      } else if (typeof currentValue === "string") {
        me.originalLocales = me.originalLocales || {};
        localizedValue = Objects.getPath(me.originalLocales, property);
        if (localizedValue === void 0) {
          Objects.setPath(me.originalLocales, property, currentValue);
          localizedValue = currentValue;
        }
        if (localizedValue) {
          Objects.setPath(me, property, localizedValue = me.optionalL(localizedValue, me));
        }
      }
      return localizedValue || currentValue;
    }
    /**
     * Method that is triggered when applying a locale to the instance
     * (happens on the instance construction steps and when switching to another locale).
     *
     * The method can be overridden to dynamically translate the instance when locale is switched.
     * When overriding the method please make sure you call `super.updateLocalization()`.
     * @category Misc
     * @advanced
     */
    updateLocalization() {
      var _a6, _b2;
      if (this.localizable !== false) {
        (_a6 = this.localizableProperties) == null ? void 0 : _a6.forEach(this.localizeProperty, this);
        (_b2 = this.trigger) == null ? void 0 : _b2.call(this, "localized");
      }
    }
    static getTranslation(text, templateData, localeCls) {
      const locale6 = LocaleManager_default.locale;
      let result = null, clsName, cls;
      if (locale6) {
        for (const { match, localeKey, localeClass } of this.parseLocaleString(text)) {
          const translate = (clsName2) => {
            var _a6;
            const translation = (_a6 = locale6[clsName2]) == null ? void 0 : _a6[localeKey];
            if (translation) {
              if (typeof translation === "function") {
                result = templateData != null ? translation(templateData) : translation;
              } else if (typeof translation === "object" || text === match) {
                result = translation;
              } else {
                result = (result || text).replace(match, translation);
              }
              if (typeof translation === "string" && translation.includes("L{")) {
                result = this.getTranslation(translation, templateData, localeCls);
              }
            }
            return translation;
          };
          let success2 = false;
          for (cls = localeCls; cls && (clsName = _a5.clsName(cls)); cls = Object.getPrototypeOf(cls)) {
            if (success2 = translate(clsName)) {
              break;
            } else if (typeof cls === "string") {
              break;
            }
          }
          if (!success2 && localeClass) {
            translate(localeClass);
          }
        }
      }
      return result;
    }
    /**
     * Get localized string, returns `null` if no localized string found.
     * @param {String} text String key
     * @param {Object} [templateData] Data to supply to template if localized string is a function
     * @returns {String}
     * @internal
     */
    static localize(text, templateData = void 0, ...localeClasses) {
      if ((localeClasses == null ? void 0 : localeClasses.length) === 0) {
        localeClasses = [this];
      }
      let translation = null;
      localeClasses.some((cls) => {
        translation = _a5.getTranslation(text, templateData, cls);
        return translation != null;
      });
      return translation;
    }
    /**
     * Get localized string, returns value of `text` if no localized string found.
     *
     * If {@link Core.localization.LocaleManager#property-throwOnMissingLocale LocaleManager.throwOnMissingLocale}
     * is `true` then calls to `L()` will throw `Localization is not found for 'text' in 'ClassName'` exception when no
     * localization is found.
     *
     * @param {String} text String key
     * @param {Object} [templateData] Data to supply to template if localized string is a function
     * @static
     * @returns {String}
     * @advanced
     */
    static L(text, templateData = void 0, ...localeClasses) {
      if ((localeClasses == null ? void 0 : localeClasses.length) === 0) {
        localeClasses = [this];
      }
      const translation = this.localize(text, templateData, ...localeClasses);
      if (translation == null && LocaleManager_default.throwOnMissingLocale && text.includes("L{")) {
        throw new Error(`Localization is not found for '${text}' in '${localeClasses.map((cls) => _a5.clsName(cls)).join(", ")}'. ${LocaleManager_default.locale.localeName ? `Locale : ${LocaleManager_default.locale.localeName}` : ""}`);
      }
      return translation != null ? translation : text;
    }
    /**
     * Convenience function that can be called directly on the class that mixes Localizable in
     *
     * ```javascript
     * button.text = grid.L('L{group}');
     * ```
     *
     * @param {String} text String key
     * @param {Object} [templateData] Data to supply to template if localized string is a function
     * @returns {String}
     * @category Misc
     * @advanced
     */
    L(text, templateData, localeClass = this.localeClass) {
      const { constructor } = this;
      if (localeClass && _a5.clsName(localeClass) !== _a5.clsName(constructor)) {
        return _a5.L(text, templateData, localeClass, constructor);
      } else {
        return _a5.L(text, templateData, constructor);
      }
    }
    /**
     * Convenience function to get an optional translation. The difference compared to `L()` is that it won't throw
     * an error when the translation is missing even if configured with `throwOnMissingLocale`
     *
     * ```javascript
     * button.text = grid.optionalL('L{group}');
     * ```
     *
     * @param {String} text String key
     * @param {Object} [templateData] Data to supply to template if localized string is a function
     * @returns {String}
     * @static
     * @category Misc
     * @advanced
     */
    static optionalL(text, templateData = void 0, ...localeClasses) {
      const shouldThrow = LocaleManager_default.throwOnMissingLocale;
      LocaleManager_default.throwOnMissingLocale = shouldThrow && localeRe.test(text);
      if ((localeClasses == null ? void 0 : localeClasses.length) === 0) {
        localeClasses = [this];
      }
      const result = _a5.L(text, templateData, ...localeClasses);
      LocaleManager_default.throwOnMissingLocale = shouldThrow;
      return result;
    }
    /**
     * Convenience function to get an optional translation. The difference compared to `L()` is that it won't throw
     * an error when the translation is missing even if configured with `throwOnMissingLocale`
     *
     * ```javascript
     * button.text = grid.optionalL('L{group}');
     * ```
     *
     * @param {String} text String key
     * @param {Object} [templateData] Data to supply to template if localized string is a function
     * @param {Boolean} [preventThrow] Prevent throwing error even if localized text matches `L{foo}`
     * @returns {String}
     * @category Misc
     * @internal
     */
    optionalL(text, templateData = this, preventThrow = false) {
      const shouldThrow = LocaleManager_default.throwOnMissingLocale;
      LocaleManager_default.throwOnMissingLocale = shouldThrow && localeRe.test(text) && !preventThrow;
      const result = this.L(text, templateData);
      LocaleManager_default.throwOnMissingLocale = shouldThrow;
      return result;
    }
    /**
     * Get the global LocaleManager
     * @property {Core.localization.LocaleManager}
     * @typings {typeof LocaleManager}
     * @category Misc
     * @readonly
     * @advanced
     */
    get localeManager() {
      return LocaleManager_default;
    }
    /**
     * Get the global LocaleHelper
     * @property {Core.localization.LocaleHelper}
     * @typings {typeof LocaleHelper}
     * @category Misc
     * @readonly
     * @advanced
     */
    get localeHelper() {
      return LocaleHelper;
    }
  }, __publicField(_a5, "$name", "Localizable"), _a5;
};

// ../Core/lib/Core/mixin/InstancePlugin.js
function getDescriptor(me, fnName) {
  const property = ObjectHelper.getPropertyDescriptor(me, fnName);
  return property && (property.get || property.set) ? property : null;
}
var InstancePlugin = class extends Base.mixin(Events_default, Localizable_default) {
  onConfigChange(...args) {
    var _a5, _b2;
    super.onConfigChange(...args);
    if (!this.isConfiguring) {
      (_b2 = (_a5 = this.client) == null ? void 0 : _a5.afterPluginConfigChange) == null ? void 0 : _b2.call(_a5, { plugin: this, ...args[0] });
    }
  }
  //endregion
  updateClient(client) {
    if (!this.owner) {
      this.owner = client;
    }
  }
  /**
   * This will merge a feature's (subclass of InstancePlugin) keyMap with it's client's keyMap.
   * @private
   */
  updateKeyMap(keyMap) {
    const { client } = this;
    client.keyMap = client.mergeKeyMaps(client.keyMap, keyMap, StringHelper.uncapitalize(this.constructor.$name));
  }
  //region Init
  /**
   * Call from another instance to add plugins to it.
   *
   * ```javascript
   * InstancePlugin.initPlugins(this, Search, Stripe);
   * ```
   *
   * @param plugInto Instance to mix into (usually this)
   * @param plugins Classes to plug in
   * @internal
   */
  static initPlugins(plugInto, ...plugins) {
    for (const PluginClass of plugins) {
      this.initPlugin(plugInto, PluginClass);
    }
  }
  /**
   * Call from another instance to add a plugin to it
   *
   * @param plugInto Instance to mix into (usually this)
   * @param PluginClass Class to plug in
   * @param config Configuration object to pass to the plugin constructor
   * @returns {Core.mixin.InstancePlugin}
   * @internal
   */
  static initPlugin(plugInto, PluginClass, config) {
    const property = plugInto.plugins || (plugInto.plugins = {}), args = [plugInto];
    if (config) {
      args.push(config);
    }
    return property[PluginClass.$$name] = new PluginClass(...args);
  }
  /**
   * Simple wrapper for {@link #property-disabled} to make optional chaining simple:
   *
   * ```javascript
   * grid.features.myFeature?.enabled // returns true when feature exists and is enabled
   * ```
   * @returns {Boolean}
   * @internal
   */
  get enabled() {
    return !this.disabled;
  }
  // We can act as an owner of a widget, so must be able to participate in focus reversion
  getFocusRevertTarget() {
    var _a5;
    return (_a5 = this.client) == null ? void 0 : _a5.getFocusRevertTarget();
  }
  construct(...args) {
    const me = this;
    let [plugInto, config] = args, listeners;
    if (args.length === 1) {
      if (ObjectHelper.isObject(plugInto)) {
        config = plugInto;
        plugInto = config.client;
      }
    } else {
      config = ObjectHelper.assign({}, config);
      delete config.client;
    }
    me.client = plugInto;
    super.construct(config);
    me.applyPluginConfig(plugInto);
    listeners = me.clientListeners;
    if (listeners) {
      listeners = ObjectHelper.assign({}, listeners);
      listeners.thisObj = me;
      plugInto.ion(listeners);
    }
  }
  /**
   * Applies config as found in plugInto.pluginConfig, or published all if no config found.
   * @private
   * @param plugInto Target instance to plug into
   */
  applyPluginConfig(plugInto) {
    const me = this, config = me.pluginConfig || me.constructor.pluginConfig;
    if (config) {
      const { assign: assign2, chain, after, before, override } = config;
      assign2 && me.applyAssign(plugInto, assign2);
      (chain || after) && me.applyChain(plugInto, chain || after);
      before && me.applyChain(plugInto, before, false);
      override && me.applyOverride(plugInto, override);
    }
  }
  /**
   * Applies assigning for specified functions.
   * @private
   * @param plugInto
   * @param fnNames
   */
  applyAssign(plugInto, fnNames) {
    fnNames.forEach((fnName) => this.assign(plugInto, fnName));
  }
  /**
   * Applies chaining for specified functions.
   * @private
   * @param plugInto
   * @param functions
   * @param after
   */
  applyChain(plugInto, functions, after = true) {
    if (Array.isArray(functions)) {
      for (const fnName of functions) {
        this.chain(plugInto, fnName, fnName, after);
      }
    } else {
      for (const intoName in functions) {
        this.chain(plugInto, intoName, functions[intoName], after);
      }
    }
  }
  /**
   * Applies override for specified functions.
   * @private
   * @param plugInto
   * @param fnNames
   */
  applyOverride(plugInto, fnNames) {
    const me = this;
    if (!me.overridden) {
      me.overridden = {};
    }
    fnNames.forEach((fnName) => {
      if (!me[fnName]) {
        throw new Error(`Trying to chain fn ${plugInto.$$name}#${fnName}, but plugin fn ${me.$$name}#${fnName} does not exist`);
      }
      if (typeof plugInto[fnName] === "function") {
        me.overridden[fnName] = plugInto[fnName].bind(plugInto);
      }
      plugInto[fnName] = me[fnName].bind(me);
    });
  }
  /**
   * Assigns specified functions.
   * @private
   * @param plugInto
   * @param fnName
   */
  assign(plugInto, fnName) {
    const me = this, property = getDescriptor(me, fnName);
    if (property) {
      Object.defineProperty(plugInto, fnName, {
        configurable: true,
        enumerable: true,
        get: property.get && property.get.bind(me),
        set: property.set && property.set.bind(me)
      });
    } else {
      plugInto[fnName] = me[fnName].bind(me);
    }
  }
  //endregion
  //region Chaining
  /**
   * Chains functions. When the function is called on the target class all functions in the chain will be called in
   * the order they were added.
   * @private
   * @param plugInto
   * @param intoName
   * @param hookName
   * @param after
   */
  chain(plugInto, intoName, hookName, after = true) {
    let prio = 0;
    if (typeof intoName === "object") {
      intoName = intoName.fn;
    }
    if (typeof hookName === "object") {
      prio = hookName.prio || 0;
      hookName = hookName.fn;
    }
    const me = this, chains = plugInto.pluginFunctionChain || (plugInto.pluginFunctionChain = {}), hookFn = me[hookName] && me[hookName].bind(me), functionChainRunner = me.functionChainRunner;
    if (!hookFn) {
      throw new Error(`Trying to chain fn ${plugInto.$$name}#${hookName}, but plugin fn ${me.$$name}#${hookName} does not exist`);
    }
    if (!chains[intoName]) {
      let intoFn = plugInto[intoName];
      if (intoFn) {
        intoFn = intoFn.bind(plugInto);
        intoFn.$this = plugInto;
        intoFn.$prio = 0;
      }
      chains[intoName] = intoFn ? [intoFn] : [];
      plugInto[intoName] = (...params) => functionChainRunner(chains[intoName], params);
    }
    hookFn.$this = me;
    hookFn.$prio = prio;
    chains[intoName][after ? "push" : "unshift"](hookFn);
    chains[intoName].$sorted = false;
  }
  /**
   * Used to run multiple plugged in functions with the same name, see chain above. Returning false from a
   * function will abort chain.
   * @private
   * @param {Array} chain
   * @param {Array} params
   * @returns {*} value returned from last function in chain (or false if any returns false)
   */
  functionChainRunner(chain, params) {
    let fn2, i, returnValue;
    if (!chain.$sorted) {
      chain.sort((a, b) => b.$prio - a.$prio);
      chain.$sorted = true;
    }
    for (i = 0; i < chain.length; i++) {
      fn2 = chain[i];
      if (!fn2.$this.isDestroyed) {
        returnValue = fn2(...params);
        if (returnValue === false) {
          break;
        }
      }
    }
    return returnValue;
  }
  //endregion
  /**
   * Called when disabling/enabling the plugin/feature, not intended to be called directly. To enable or disable a
   * plugin/feature, see {@link #property-disabled}.
   *
   * By default removes the cls of the plugin from its client. Override in subclasses to take any other actions necessary.
   * @category Misc
   * @advanced
   */
  doDisable(disable) {
    var _a5, _b2, _c2, _d;
    const me = this, { constructor } = me, cls = "featureClass" in constructor ? constructor.featureClass : `b-${constructor.$$name.toLowerCase()}`, key = StringHelper.uncapitalize(constructor.$$name);
    if (cls) {
      (_b2 = (_a5 = me.client) == null ? void 0 : _a5._element) == null ? void 0 : _b2.classList[disable ? "remove" : "add"](cls);
    }
    if (!me.isConfiguring) {
      if (disable) {
        me.trigger("disable");
      } else {
        me.trigger("enable");
      }
      (_d = (_c2 = me.client).syncSplits) == null ? void 0 : _d.call(_c2, (other) => {
        const otherFeature = other.features[key];
        if (otherFeature) {
          otherFeature.disabled = disable;
        }
      });
    }
  }
  updateDisabled(disabled) {
    this.doDisable(disabled);
  }
  throwOverrideIsMissing(data) {
    throw new Error(`Trying to override fn ${data.plugIntoName}#${data.fnName}, but plugin fn ${data.pluginName}#${data.fnName} does not exist`);
  }
  // Convenience method to read the rootElement from the owner widget
  get rootElement() {
    return this.client.rootElement;
  }
  get state() {
    var _a5;
    return (_a5 = this.constructor.stateProperties) == null ? void 0 : _a5.reduce((acc, prop) => {
      acc[prop] = this[prop];
      return acc;
    }, {});
  }
};
__publicField(InstancePlugin, "$name", "InstancePlugin");
//region Config
__publicField(InstancePlugin, "configurable", {
  /**
   * The plugin/feature `disabled` state.
   *
   * For a feature that is **off** by default that you want to enable later during runtime,
   * configure it with `disabled : true`.
   * ```javascript
   * const grid = new Grid({
   *      features : {
   *          featureName : {
   *              disabled : true // on and disabled, can be enabled later
   *          }
   *      }
   * });
   *
   * // enable the feature
   * grid.features.featureName.disabled = false;
   * ```
   *
   * If the feature is **disabled** by default, and you want to include and enable the feature, configure it as `true`:
   * ```javascript
   * const grid = new Grid({
   *      features : {
   *          featureName : true // on and enabled, can be disabled later
   *      }
   * });
   *
   * // disable the feature
   * grid.features.featureName.disabled = true;
   * ```
   *
   * If the feature is **on** by default, but you want to turn it **off**, configure it as `false`:
   * ```javascript
   * const grid = new Grid({
   *      features : {
   *          featureName : false // turned off, not included at all
   *      }
   * });
   * ```
   *
   * If the feature is **enabled** by default and you have no need of reconfiguring it,
   * you can omit the feature configuration.
   *
   * @prp {Boolean}
   * @typings {Boolean|'inert'}
   * @default
   * @category Common
   */
  disabled: false,
  /**
   * The Widget which was passed into the constructor,
   * which is the Widget we are providing extra services for.
   * @member {Core.widget.Widget} client
   * @readonly
   * @category Misc
   * @advanced
   */
  /**
   * The widget which this plugin is to attach to.
   * @config {Core.widget.Widget}
   * @category Misc
   * @advanced
   */
  client: null,
  // The plugins can define their own keyMap which will then be merged with their client's keyMap.
  keyMap: null
  /**
   * @hideconfigs bubbleEvents, callOnFunctions
   */
  /**
   * @hidefunctions downloadTestCase, destroy
   */
  /**
   * @hideproperties isDestroyed
   */
  /**
   * @hideevents destroy, beforeDestroy
   */
});
__publicField(InstancePlugin, "properties", {
  clientListeners: null
});
InstancePlugin._$name = "InstancePlugin";

// ../Core/lib/Core/mixin/Pluggable.js
var Pluggable_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    /**
     * Specify plugins (an array of classes) in config
     * @config {Function[]} plugins
     * @category Misc
     * @advanced
     */
    /**
     * Map of applied plugins
     * @property {Object<String,Core.mixin.InstancePlugin>}
     * @readonly
     * @category Misc
     * @advanced
     */
    get plugins() {
      if (!this._plugins) {
        this._plugins = {};
      }
      return this._plugins;
    }
    set plugins(plugins) {
      if (plugins) {
        if (!Array.isArray(plugins))
          plugins = [plugins];
        InstancePlugin.initPlugins(this, ...plugins);
      }
      this.initPlugins();
    }
    /**
     * Template method which may be implemented in subclasses to initialize any plugins.
     * This method is empty in the `Pluggable` base class.
     * @internal
     */
    initPlugins() {
    }
    /**
     * Adds plugins to an instance.
     * @param {Function[]} plugins The plugins to add
     * @category Misc
     * @advanced
     */
    addPlugins(...plugins) {
      InstancePlugin.initPlugins(this, ...plugins);
    }
    /**
     * Adds a plugin to an instance, with a configuration object passed to the plugin constructor
     * @param plugin
     * @param config
     * @returns {Core.mixin.InstancePlugin}
     * @internal
     */
    addPlugin(plugin, config) {
      return InstancePlugin.initPlugin(this, plugin, config);
    }
    /**
     * Checks if instance has plugin.
     * @param {String|Function} pluginClassOrName Plugin or name to check for
     * @returns {Boolean}
     * @category Misc
     * @advanced
     */
    hasPlugin(pluginClassOrName) {
      return this.getPlugin(pluginClassOrName) != null;
    }
    /**
     * Get a plugin instance.
     * @param {String|Function} pluginClassOrName
     * @returns {Core.mixin.InstancePlugin}
     * @category Misc
     * @advanced
     */
    getPlugin(pluginClassOrName) {
      var _a6;
      if (typeof pluginClassOrName === "function") {
        pluginClassOrName = pluginClassOrName.$$name;
      }
      return (_a6 = this.plugins) == null ? void 0 : _a6[pluginClassOrName];
    }
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "Pluggable"), _a5;
};

// ../Core/lib/Core/localization/En.js
var locale = {
  localeName: "En",
  localeDesc: "English (US)",
  localeCode: "en-US",
  localeRtl: false,
  Object: {
    Yes: "Yes",
    No: "No",
    Cancel: "Cancel",
    Ok: "OK",
    Week: "Week",
    None: "None",
    previous: "Previous",
    next: "Next",
    to: "to",
    at: "At",
    on: "On",
    editing: (what) => `Editing ${what}`,
    settings: "Settings",
    close: "Close",
    go: "Go",
    save: "Save",
    cancel: "Cancel",
    revert: "Revert",
    today: "Today"
  },
  CheckboxGroup: {
    wrongNbrOfOptionsSelected: (min4) => `Select exactly ${min4}`,
    tooFewOptionsSelected: (min4) => `Select at least ${min4}`,
    tooManyOptionsSelected: (max3) => `Select no more than ${max3}`
  },
  CodeEditor: {
    apply: "Apply",
    autoApply: "Auto apply",
    downloadCode: "Download code",
    editor: "Code editor",
    viewer: "Code viewer"
  },
  ColorPicker: {
    noColor: "No color"
  },
  Combo: {
    noResults: "No results",
    recordNotCommitted: "Record could not be added",
    addNewValue: (value2) => `Add ${value2}`
  },
  FilePicker: {
    file: "File"
  },
  Field: {
    badInput: "Invalid field value",
    patternMismatch: "Value should match a specific pattern",
    rangeOverflow: (value2) => `Value must be less than or equal to ${value2.max}`,
    rangeUnderflow: (value2) => `Value must be greater than or equal to ${value2.min}`,
    stepMismatch: "Value should fit the step",
    tooLong: "Value should be shorter",
    tooShort: "Value should be longer",
    typeMismatch: "Value is required to be in a special format",
    valueMissing: "This field is required",
    invalidValue: "Invalid field value",
    minimumValueViolation: "Minimum value violation",
    maximumValueViolation: "Maximum value violation",
    fieldRequired: "This field is required",
    validateFilter: "Value must be selected from the list"
  },
  DateField: {
    invalidDate: (format2) => "Invalid date input, expected format is " + format2
  },
  DatePicker: {
    gotoPrevYear: "Go to previous year",
    gotoPrevMonth: "Go to previous month",
    gotoNextMonth: "Go to next month",
    gotoNextYear: "Go to next year"
  },
  Hint: {
    dontShowAgain: "Don't show again"
  },
  NumberFormat: {
    locale: "en-US",
    currency: "USD"
  },
  DurationField: {
    invalidUnit: "Invalid unit"
  },
  TimeField: {
    invalidTime: "Invalid time input"
  },
  TimePicker: {
    hour: "Hour",
    minute: "Minute",
    second: "Second"
  },
  List: {
    loading: "Loading...",
    selectAll: "Select All"
  },
  GridBase: {
    loadMask: "Loading...",
    syncMask: "Saving changes, please wait..."
  },
  PagingToolbar: {
    firstPage: "Go to first page",
    prevPage: "Go to previous page",
    page: "Page",
    nextPage: "Go to next page",
    lastPage: "Go to last page",
    reload: "Reload current page",
    noRecords: "No records to display",
    pageCountTemplate: (data) => `of ${data.lastPage}`,
    summaryTemplate: (data) => `Displaying records ${data.start} - ${data.end} of ${data.allCount}`
  },
  PanelCollapser: {
    Collapse: "Collapse",
    Expand: "Expand"
  },
  Popup: {
    close: "Close",
    maximize: "Maximize"
  },
  TabPanel: {
    containsInvalidFields: "Tab contains invalid fields"
  },
  Toolbar: {
    showOverflowingItems: "Show overflowing items",
    scrollForMore: "Scroll for more"
  },
  UndoRedo: {
    Undo: "Undo",
    Redo: "Redo",
    UndoLastAction: "Undo last action",
    RedoLastAction: "Redo last undone action",
    NoActions: "No items in the undo queue"
  },
  FieldFilterPicker: {
    equals: "equals",
    doesNotEqual: "does not equal",
    isEmpty: "empty",
    isNotEmpty: "not empty",
    contains: "contains",
    doesNotContain: "does not contain",
    startsWith: "starts with",
    endsWith: "ends with",
    isOneOf: "one of",
    isNotOneOf: "not one of",
    isGreaterThan: "greater than",
    isLessThan: "less than",
    isGreaterThanOrEqualTo: "greater or equals",
    isLessThanOrEqualTo: "less or equals",
    isBetween: "between",
    isNotBetween: "not between",
    isBefore: "before",
    isAfter: "after",
    timeEquals: "time equals",
    timeDoesNotEqual: "time does not equal",
    isToday: "today",
    isTomorrow: "tomorrow",
    isYesterday: "yesterday",
    isThisWeek: "this week",
    isNextWeek: "next week",
    isLastWeek: "last week",
    isThisMonth: "this month",
    isNextMonth: "next month",
    isLastMonth: "last month",
    isThisYear: "this year",
    isNextYear: "next year",
    isLastYear: "last year",
    isYearToDate: "year to date",
    isTrue: "true",
    isFalse: "false",
    selectAProperty: "Select property",
    selectAnOperator: "Select operator",
    caseSensitive: "Case-sensitive",
    and: "and",
    dateFormat: "D/M/YY",
    selectValue: "Select value",
    selectOneOrMoreValues: "Select value(s)",
    enterAValue: "Enter value",
    enterANumber: "Enter number",
    selectADate: "Select date",
    selectATime: "Select time"
  },
  FieldFilterPickerGroup: {
    addFilter: "Add filter",
    applyCond: (applied) => applied ? "Skip filter (currently applied)" : "Apply filter (currently skipped)"
  },
  DateHelper: {
    locale: "en-US",
    weekStartDay: 0,
    nonWorkingDays: {
      0: true,
      6: true
    },
    weekends: {
      0: true,
      6: true
    },
    unitNames: [
      { single: "millisecond", plural: "ms", abbrev: "ms" },
      { single: "emillisecond", plural: "ems", abbrev: "ems" },
      { single: "second", plural: "seconds", abbrev: "s" },
      { single: "esecond", plural: "eseconds", abbrev: "es" },
      { single: "minute", plural: "minutes", abbrev: "min" },
      { single: "eminute", plural: "eminutes", abbrev: "emin" },
      { single: "hour", plural: "hours", abbrev: "h" },
      { single: "ehour", plural: "ehours", abbrev: "eh" },
      { single: "day", plural: "days", abbrev: "d" },
      { single: "eday", plural: "edays", abbrev: "ed" },
      { single: "week", plural: "weeks", abbrev: "w" },
      { single: "eweek", plural: "eweeks", abbrev: "ew" },
      { single: "month", plural: "months", abbrev: "mon" },
      { single: "emonth", plural: "emonths", abbrev: "emon" },
      { single: "quarter", plural: "quarters", abbrev: "q" },
      { single: "equarter", plural: "equarters", abbrev: "eq" },
      { single: "year", plural: "years", abbrev: "yr" },
      { single: "eyear", plural: "eyears", abbrev: "eyr" },
      { single: "decade", plural: "decades", abbrev: "dec" },
      { single: "edecade", plural: "edecades", abbrev: "edec" }
    ],
    unitAbbreviations: [
      ["mil"],
      ["emil"],
      ["s", "sec"],
      ["es", "esec"],
      ["m", "min"],
      ["em", "emin"],
      ["h", "hr"],
      ["eh", "ehr"],
      ["d"],
      ["ed"],
      ["w", "wk"],
      ["ew", "ewk"],
      ["mo", "mon", "mnt", "M"],
      ["emo", "emon", "emnt"],
      ["q", "quar", "qrt"],
      ["eq", "equar", "eqrt"],
      ["y", "yr"],
      ["ey", "eyr"],
      ["dec"],
      ["edec"]
    ],
    parsers: {
      L: "MM/DD/YYYY",
      LT: "HH:mm A",
      LTS: "HH:mm:ss A"
    },
    ordinalSuffix: (number) => {
      const hasSpecialCase = ["11", "12", "13"].find((n) => number.endsWith(n));
      let suffix = "th";
      if (!hasSpecialCase) {
        const lastDigit = number[number.length - 1];
        suffix = { 1: "st", 2: "nd", 3: "rd" }[lastDigit] || "th";
      }
      return number + suffix;
    },
    decade: (date2) => {
      const year = typeof date2 === "number" ? date2 : date2.getFullYear();
      return `${year - year % 10}s`;
    }
  }
};
var En_default = LocaleHelper.publishLocale(locale);

// ../Core/lib/Core/helper/DateHelper.js
var { toString: toString3 } = Object.prototype;
var DATE_TYPE2 = toString3.call(/* @__PURE__ */ new Date());
var tempDate = /* @__PURE__ */ new Date();
var MS_PER_HOUR = 1e3 * 60 * 60;
var defaultValue = (value2, defValue) => isNaN(value2) || value2 == null ? defValue : value2;
var rangeFormatPartRe = /([ES]){([^}]+)}/g;
var enOrdinalSuffix = (number) => {
  const hasSpecialCase = ["11", "12", "13"].find((n) => number.endsWith(n));
  let suffix = "th";
  if (!hasSpecialCase) {
    const lastDigit = number[number.length - 1];
    suffix = { 1: "st", 2: "nd", 3: "rd" }[lastDigit] || "th";
  }
  return number + suffix;
};
var defineUnitConversion = (fromUnit, toUnit, conversionRate, targetObject = validConversions) => {
  targetObject[fromUnit] = targetObject[fromUnit] || {
    [fromUnit]: 1
  };
  targetObject[toUnit] = targetObject[toUnit] || {
    [toUnit]: 1
  };
  targetObject[fromUnit][toUnit] = conversionRate;
  targetObject[toUnit][fromUnit] = 1 / conversionRate;
  return targetObject;
};
var useIntlFormat = (name, options, date2) => {
  const formatter = intlFormatterCache[name] || (intlFormatterCache[name] = new Intl.DateTimeFormat(locale2, options));
  return formatter.format(date2);
};
var formatTime = (name, options, date2, isShort = false) => {
  let strTime = useIntlFormat(name, options, date2);
  if (/am|pm/i.test(strTime)) {
    strTime = strTime.replace(/^0/, "");
    if (isShort) {
      strTime = strTime.replace(/:00/, "");
    }
  }
  return strTime;
};
var getDayDiff = (end, start) => Math.floor((end.getTime() - start.getTime() - (end.getTimezoneOffset() - start.getTimezoneOffset()) * validConversions.minute.millisecond) / validConversions.day.millisecond) + 1;
var normalizeDay = (day2) => day2 >= 0 ? day2 : day2 + 7;
var msRegExp = /([^\w])(S+)/gm;
var msReplacer = (match, g1) => g1 + "SSS";
var splitRegExp = /[:.\-/\s]/;
var dayPrefixRe = /^(?:יום |ال)/;
var timeZoneFormatRe = /(Z{1,2})$/;
var timeZoneRegExp = /(?:Z|[+-]\d{2}:\d{2})$/;
var locale2 = "en-US";
var ordinalSuffix = enOrdinalSuffix;
var formatCache = {};
var formatRedirects = {};
var intlFormatterCache = {};
var parserCache = {};
var keyCache = {};
var amIndicator = "AM";
var pmIndicator = "PM";
var keyDivisor = MS_PER_HOUR;
var dayName;
var dayPrefix;
var redirectFormat = (format2) => {
  const intlConfig = intlFormatConfigs[format2];
  if (!intlConfig) {
    throw new Error("Only international formats should be used here");
  }
  if (formatRedirects[format2] !== void 0) {
    return formatRedirects[format2];
  }
  const intl = new Intl.DateTimeFormat(locale2, intlConfig), fmt = intl.formatToParts(new Date(2001, 1, 2, 3, 4, 5, 6)).map((part) => {
    const type = part.type, intlCfg = intlConfig[type];
    if (type === "literal") {
      return part.value.replace(/,/g, "");
    } else if (type === "day") {
      return intlCfg === "numeric" ? "D" : "DD";
    } else if (type === "month") {
      return intlCfg === "short" ? "MMM" : intlCfg === "long" ? "MMMM" : intlCfg === "numeric" ? "M" : "MM";
    } else if (type === "year") {
      return intlCfg === "numeric" ? "YYYY" : "YY";
    }
  }).join("");
  return formatRedirects[format2] = fmt;
};
var DEFAULT_YEAR = 2020;
var DEFAULT_MONTH = 0;
var DEFAULT_DAY = 1;
var intlFormatConfigs = {
  l: { year: "numeric", month: "numeric", day: "numeric" },
  ll: { year: "numeric", month: "short", day: "numeric" }
};
var formats = {
  // 1, 2, ... 11, 12
  M: (date2) => date2.getMonth() + 1,
  //date.toLocaleDateString(locale, { month : 'numeric' }),
  // 1st, 2nd, 3rd, 4th, ... 11th, 12th
  Mo: (date2) => ordinalSuffix(formats.M(date2).toString()),
  // 01, 02, ...
  MM: (date2) => (date2.getMonth() + 1).toString().padStart(2, "0"),
  //date.toLocaleDateString(locale, { month : '2-digit' }),
  // Jan, Feb, ...
  MMM: (date2) => useIntlFormat("MMM", { month: "short" }, date2),
  // January, February, ...
  MMMM: (date2) => useIntlFormat("MMMM", { month: "long" }, date2),
  // Decade, eg 17-Jun-1962 => 1960s
  DC: (date2) => DateHelper.localize("L{decade}", date2),
  // 1, 2, ...
  Q: (date2) => Math.ceil((date2.getMonth() + 1) / 3),
  // 1st, 2nd, ...
  Qo: (date2) => ordinalSuffix(formats.Q(date2).toString()),
  // 1, 2, ...
  D: (date2) => date2.getDate(),
  //date.toLocaleDateString(locale, { day : 'numeric' }),
  // 1st, 2nd, ...
  Do: (date2) => ordinalSuffix(formats.D(date2).toString()),
  // 01, 02, ...
  DD: (date2) => date2.getDate().toString().padStart(2, "0"),
  //date.toLocaleDateString(locale, { day : '2-digit' }),
  // 1, 2, ..., 365, 365
  DDD: (date2) => Math.ceil(
    (new Date(date2.getFullYear(), date2.getMonth(), date2.getDate(), 12, 0, 0) - new Date(date2.getFullYear(), 0, 0, 12, 0, 0)) / validConversions.day.millisecond
  ),
  // 1st, 2nd, ...
  DDDo: (date2) => ordinalSuffix(formats.DDD(date2).toString()),
  // 001, 002, ...
  DDDD: (date2) => formats.DDD(date2).toString().padStart(3, "0"),
  // 0, 1, ..., 6
  d: (date2) => date2.getDay(),
  // 0th, 1st, ...
  do: (date2) => ordinalSuffix(date2.getDay().toString()),
  // S, M, ...
  d1: (date2) => useIntlFormat("d1", { weekday: "narrow" }, date2).substr(0, 1),
  // Su, Mo, ...
  dd: (date2) => formats.ddd(date2).substring(0, 2),
  // Sun, Mon, ...
  ddd: (date2) => {
    var _a5;
    dayName = useIntlFormat("ddd", { weekday: "short" }, date2);
    dayPrefix = (_a5 = dayName.match(dayPrefixRe)) == null ? void 0 : _a5[0];
    return dayName.substring(dayPrefix ? dayPrefix.length : 0);
  },
  // Sunday, Monday, ...
  dddd: (date2) => {
    var _a5;
    dayName = useIntlFormat("dddd", { weekday: "long" }, date2);
    dayPrefix = (_a5 = dayName.match(dayPrefixRe)) == null ? void 0 : _a5[0];
    return dayName.substring(dayPrefix ? dayPrefix.length : 0);
  },
  u: (date2) => {
    const formatter = intlFormatterCache.u || (intlFormatterCache.u = new Intl.DateTimeFormat("en-GB", {
      timeZone: "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    })), parts = formatter.formatToParts(date2);
    return `${parts[4].value}${parts[2].value}${parts[0].value}Z`;
  },
  uu: (date2) => {
    const formatter = intlFormatterCache.uu || (intlFormatterCache.uu = new Intl.DateTimeFormat("en-GB", {
      timeZone: "UTC",
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    })), parts = formatter.formatToParts(date2);
    return `${parts[4].value}${parts[2].value}${parts[0].value}T${parts[6].value}${parts[8].value}${parts[10].value}Z`;
  },
  e: (date2) => date2.getDay(),
  E: (date2) => date2.getDay() + 1,
  // ISO week, 1, 2, ...
  W: (date2) => DateHelper.getWeekNumber(date2)[1],
  Wo: (date2) => ordinalSuffix(formats.W(date2).toString()),
  WW: (date2) => formats.W(date2).toString().padStart(2, "0"),
  // ISO week, 1, 2, ... with localized 'Week ' prefix
  Wp: (date2) => `${DateHelper.localize("L{Week}")} ${formats.W(date2)}`,
  WWp: (date2) => `${DateHelper.localize("L{Week}")} ${formats.WW(date2)}`,
  Wp0: (date2) => `${DateHelper.localize("L{Week}")[0]}${formats.W(date2)}`,
  WWp0: (date2) => `${DateHelper.localize("L{Week}")[0]}${formats.WW(date2)}`,
  // 1979, 2018
  Y: (date2) => date2.getFullYear(),
  //date.toLocaleDateString(locale, { year : 'numeric' }),
  // 79, 18
  YY: (date2) => (date2.getFullYear() % 100).toString().padStart(2, "0"),
  //date.toLocaleDateString(locale, { year : '2-digit' }),
  // 1979, 2018
  YYYY: (date2) => date2.getFullYear(),
  //date.toLocaleDateString(locale, { year : 'numeric' }),
  A: (date2) => (date2.getHours() < 12 ? amIndicator : pmIndicator).toLocaleUpperCase(),
  a: (date2) => (date2.getHours() < 12 ? amIndicator : pmIndicator).toLocaleLowerCase(),
  // 0, 1, ... 23
  H: (date2) => date2.getHours(),
  // 00, 01, ...
  HH: (date2) => date2.getHours().toString().padStart(2, "0"),
  // 1, 2, ... 12
  h: (date2) => date2.getHours() % 12 || 12,
  // 01, 02, ...
  hh: (date2) => formats.h(date2).toString().padStart(2, "0"),
  // 1, 2, ... 24
  k: (date2) => date2.getHours() || 24,
  // 01, 02, ...
  kk: (date2) => formats.k(date2).toString().padStart(2, "0"),
  // Locale specific (0 -> 24 or 1 AM -> 12 PM)
  K: (date2) => formatTime("K", { hour: "numeric" }, date2),
  // Locale specific (00 -> 24 or 1 AM -> 12 PM)
  KK: (date2) => formatTime("KK", { hour: "2-digit" }, date2),
  // 0, 1, ... 59
  m: (date2) => date2.getMinutes(),
  //date.toLocaleTimeString(locale, { minute : 'numeric' }),
  // 00, 01, ...
  mm: (date2) => formats.m(date2).toString().padStart(2, "0"),
  // 0, 1, ... 59
  s: (date2) => date2.getSeconds(),
  //date.toLocaleTimeString(locale, { second : 'numeric' }),
  // 00, 01, ...
  ss: (date2) => formats.s(date2).toString().padStart(2, "0"),
  // 0, 1, ... 9 which are 000, 100, 200 ... 900 in milliseconds
  S: (date2) => Math.floor(date2.getMilliseconds() / 100).toString(),
  // 00, 01, ... 99 which are 000, 010, 020 ... 990 in milliseconds
  SS: (date2) => Math.floor(date2.getMilliseconds() / 10).toString().padStart(2, "0"),
  // 000, 001, ... 999 in milliseconds
  SSS: (date2) => date2.getMilliseconds().toString().padStart(3, "0"),
  z: (date2) => useIntlFormat("z", { timeZoneName: "short" }, date2),
  zz: (date2) => useIntlFormat("zz", { timeZoneName: "long" }, date2),
  Z: (date2) => DH2.getGMTOffset(date2),
  LT: (date2) => formatTime("LT", { hour: "2-digit", minute: "2-digit" }, date2),
  // if minutes is 0, doesn't show it
  LST: (date2) => formatTime("LST", { hour: "numeric", minute: "2-digit" }, date2, true),
  LTS: (date2) => formatTime("LTS", { hour: "2-digit", minute: "2-digit", second: "2-digit" }, date2),
  L: (date2) => useIntlFormat("L", { year: "numeric", month: "2-digit", day: "2-digit" }, date2),
  l: (date2) => useIntlFormat("l", intlFormatConfigs.l, date2),
  LL: (date2) => useIntlFormat("LL", { year: "numeric", month: "long", day: "numeric" }, date2),
  ll: (date2) => useIntlFormat("ll", intlFormatConfigs.ll, date2),
  LLL: (date2) => useIntlFormat("LLL", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }, date2),
  lll: (date2) => useIntlFormat("lll", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }, date2),
  LLLL: (date2) => useIntlFormat("LLLL", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    weekday: "long"
  }, date2),
  llll: (date2) => useIntlFormat("llll", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    weekday: "short"
  }, date2)
};
var formatKeys = Object.keys(formats).sort((a, b) => b.length - a.length);
var formatRegexp = `^(?:${formatKeys.join("|")})`;
var emptyFn2 = () => ({});
var isNumber = (str) => numberRegex.test(str);
var parseMilliseconds = (str) => isNumber(str) && { milliseconds: parseInt(str.padEnd(3, "0").substring(0, 3)) };
var parsers = {
  YYYY: (str) => {
    const year = parseInt(str);
    return { year: year >= 1e3 && year <= 9999 ? year : NaN };
  },
  Y: (str) => ({ year: parseInt(str) }),
  YY: (str) => {
    const year = parseInt(str);
    return { year: year + (year > 1968 ? 1900 : 2e3) };
  },
  M: (str) => ({ month: parseInt(str) - 1 }),
  MM: (str) => ({ month: parseInt(str) - 1 }),
  Mo: (str) => ({ month: parseInt(str) - 1 }),
  MMM: (str) => {
    const month2 = (str || "").toLowerCase();
    for (const [name, entry] of Object.entries(DateHelper._monthShortNamesIndex)) {
      if (month2.startsWith(name)) {
        return { month: entry.value };
      }
    }
  },
  MMMM: (str) => {
    const month2 = (str || "").toLowerCase();
    for (const [name, entry] of Object.entries(DateHelper._monthNamesIndex)) {
      if (month2.startsWith(name)) {
        return { month: entry.value };
      }
    }
  },
  DD: (str) => ({ date: parseInt(str) }),
  D: (str) => ({ date: parseInt(str) }),
  Do: (str) => ({ date: parseInt(str) }),
  DDD: emptyFn2,
  DDDo: emptyFn2,
  DDDD: emptyFn2,
  d: emptyFn2,
  do: emptyFn2,
  d1: emptyFn2,
  dd: emptyFn2,
  ddd: emptyFn2,
  dddd: emptyFn2,
  Q: emptyFn2,
  Qo: emptyFn2,
  W: emptyFn2,
  Wo: emptyFn2,
  WW: emptyFn2,
  e: emptyFn2,
  E: emptyFn2,
  HH: (str) => ({ hours: parseInt(str) }),
  hh: (str) => ({ hours: parseInt(str) }),
  mm: (str) => ({ minutes: parseInt(str) }),
  H: (str) => ({ hours: parseInt(str) }),
  m: (str) => ({ minutes: parseInt(str) }),
  ss: (str) => ({ seconds: parseInt(str) }),
  s: (str) => ({ seconds: parseInt(str) }),
  S: parseMilliseconds,
  SS: parseMilliseconds,
  SSS: parseMilliseconds,
  A: (str) => ({ amPm: str.toLowerCase() }),
  a: (str) => ({ amPm: str.toLowerCase() }),
  L: "MM/DD/YYYY",
  LT: "HH:mm A",
  LTS: "HH:mm:ss A",
  l: { type: "dynamic", parser: () => redirectFormat("l") },
  ll: { type: "dynamic", parser: () => redirectFormat("ll") },
  // Can either be Z (=UTC, 0) or +-HH:MM
  Z: (str) => {
    if (!str || !timeZoneRegEx.test(str) && str !== "Z") {
      return null;
    }
    let timeZone = 0;
    if (str !== "Z") {
      const matches = timeZoneRegEx.exec(str);
      if (matches) {
        const sign = matches[1] === "+" ? 1 : -1, hours2 = parseInt(matches[2]) || 0, minutes = parseInt(matches[3]) || 0;
        timeZone = sign * (hours2 * 60 + minutes);
      } else {
        timeZone = -1 * (/* @__PURE__ */ new Date()).getTimezoneOffset();
      }
    }
    return { timeZone };
  }
};
var parserKeys = Object.keys(parsers).sort((a, b) => b.length - a.length);
var parserRegexp = new RegExp(`(${parserKeys.join("|")})`);
var localeStrRegExp = new RegExp("^(LL|LLL|lll|LLLL|llll)$");
var validConversions = {
  // The units below assume:
  // 30 days in a month, 91 days for a quarter and 365 for a year
  // 52 weeks per year, 4 per month, 13 per quarter
  // 3652 days per decade (assuming two of the years will be leap with 366 days)
  decade: {
    decade: 1,
    year: 10,
    quarter: 40,
    month: 120,
    week: 520,
    day: 3652,
    hour: 24 * 3652,
    minute: 1440 * 3652,
    second: 86400 * 3652,
    millisecond: 864e5 * 3652
  },
  year: {
    decade: 0.1,
    year: 1,
    quarter: 4,
    month: 12,
    week: 52,
    day: 365,
    hour: 24 * 365,
    minute: 1440 * 365,
    second: 86400 * 365,
    millisecond: 864e5 * 365
  },
  quarter: {
    decade: 1 / 40,
    year: 1 / 4,
    quarter: 1,
    month: 3,
    week: 4,
    day: 91,
    hour: 24 * 91,
    minute: 1440 * 91,
    second: 86400 * 91,
    millisecond: 864e5 * 91
  },
  month: {
    decade: 1 / 120,
    year: 1 / 12,
    quarter: 1 / 3,
    month: 1,
    week: 4,
    day: -30,
    hour: -24 * 30,
    minute: -1440 * 30,
    second: -86400 * 30,
    millisecond: -864e5 * 30
  },
  week: {
    decade: -1 / 520,
    year: -1 / 52,
    quarter: -1 / 13,
    month: -1 / 4,
    day: 7,
    hour: 168,
    minute: 10080,
    second: 604800,
    millisecond: 6048e5
  },
  day: {
    decade: -1 / 3652,
    year: -1 / 365,
    quarter: -1 / 91,
    month: -1 / 30,
    week: 1 / 7,
    hour: 24,
    minute: 1440,
    second: 86400,
    millisecond: 864e5
  },
  hour: {
    decade: -1 / (3652 * 24),
    year: -1 / (365 * 24),
    quarter: -1 / (91 * 24),
    month: -1 / (30 * 24),
    week: 1 / 168,
    day: 1 / 24,
    minute: 60,
    second: 3600,
    millisecond: 36e5
  },
  minute: {
    decade: -1 / (3652 * 1440),
    year: -1 / (365 * 1440),
    quarter: -1 / (91 * 1440),
    month: -1 / (30 * 1440),
    week: 1 / 10080,
    day: 1 / 1440,
    hour: 1 / 60,
    second: 60,
    millisecond: 6e4
  },
  second: {
    decade: -1 / (3652 * 86400),
    year: -1 / (365 * 86400),
    quarter: -1 / (91 * 86400),
    month: -1 / (30 * 86400),
    week: 1 / 604800,
    day: 1 / 86400,
    hour: 1 / 3600,
    minute: 1 / 60,
    millisecond: 1e3
  },
  millisecond: {
    decade: -1 / (3652 * 864e5),
    year: -1 / (365 * 864e5),
    quarter: -1 / (91 * 864e5),
    month: -1 / (30 * 864e5),
    week: 1 / 6048e5,
    day: 1 / 864e5,
    hour: 1 / 36e5,
    minute: 1 / 6e4,
    second: 1 / 1e3
  }
};
var normalizedUnits = {
  ms: "millisecond",
  milliseconds: "millisecond",
  ems: "emillisecond",
  emilliseconds: "emillisecond",
  s: "second",
  seconds: "second",
  es: "esecond",
  eseconds: "esecond",
  m: "minute",
  mi: "minute",
  min: "minute",
  minutes: "minute",
  em: "eminute",
  emi: "eminute",
  emin: "eminute",
  eminutes: "eminute",
  h: "hour",
  hours: "hour",
  eh: "ehour",
  ehours: "ehour",
  d: "day",
  days: "day",
  ed: "eday",
  edays: "eday",
  w: "week",
  weeks: "week",
  ew: "eweek",
  eweeks: "eweek",
  M: "month",
  mo: "month",
  mon: "month",
  months: "month",
  EM: "emonth",
  eM: "emonth",
  emo: "emonth",
  emon: "emonth",
  emonths: "emonth",
  q: "quarter",
  quarters: "quarter",
  eq: "equarter",
  equarters: "equarter",
  y: "year",
  years: "year",
  ey: "eyear",
  eyears: "eyear",
  dec: "decade",
  decades: "decade",
  edec: "edecade",
  edecades: "edecade"
};
var withDecimalsDurationRegex = /^\s*([-+]?\d+(?:[.,]\d*)?|[-+]?(?:[.,]\d+))\s*([^\s]+)?/i;
var noDecimalsDurationRegex = /^\s*([-+]?\d+)(?![.,])\s*([^\s]+)?/i;
var canonicalUnitNames = [
  "millisecond",
  "emillisecond",
  "second",
  "esecond",
  "minute",
  "eminute",
  "hour",
  "ehour",
  "day",
  "eday",
  "week",
  "eweek",
  "month",
  "emonth",
  "quarter",
  "equarter",
  "year",
  "eyear",
  "decade",
  "edecade"
];
var canonicalUnitAbbreviations = [
  ["ms", "mil"],
  ["ems", "emil"],
  ["s", "sec"],
  ["es", "esec"],
  ["m", "min"],
  ["em", "emin"],
  ["h", "hr"],
  ["eh", "ehr"],
  ["d"],
  ["ed"],
  ["w", "wk"],
  ["ew", "ewk"],
  ["mo", "mon", "mnt", "M"],
  ["emo", "emon", "emnt"],
  ["q", "quar", "qrt"],
  ["eq", "equar", "eqrt"],
  ["y", "yr"],
  ["ey", "eyr"],
  ["dec"],
  ["edec"]
];
var deltaUnits = [
  "decade",
  "year",
  "month",
  "week",
  "day",
  "hour",
  "minute",
  "second",
  "millisecond"
];
var dateProperties = [
  "milliseconds",
  "seconds",
  "minutes",
  "hours",
  "date",
  "month",
  "year"
];
var parseNumber = (n) => {
  const result = parseFloat(n);
  return isNaN(result) ? null : result;
};
var numberRegex = /^[0-9]+$/;
var timeZoneRegEx = /([+-])(\d\d):*(\d\d)*$/;
var unitChain = {
  // standard units chain
  millisecond: {
    magnitude: 0,
    smaller: void 0,
    greater: "second"
  },
  second: {
    magnitude: 2,
    smaller: "millisecond",
    greater: "minute"
  },
  minute: {
    magnitude: 4,
    smaller: "second",
    greater: "hour"
  },
  hour: {
    magnitude: 6,
    smaller: "minute",
    greater: "day"
  },
  day: {
    magnitude: 8,
    smaller: "hour",
    greater: "week"
  },
  week: {
    magnitude: 10,
    smaller: "day",
    greater: "month"
  },
  month: {
    magnitude: 12,
    smaller: "week",
    greater: "quarter"
  },
  quarter: {
    magnitude: 14,
    smaller: "month",
    greater: "year"
  },
  year: {
    magnitude: 16,
    smaller: "quarter",
    greater: "decade"
  },
  decade: {
    magnitude: 18,
    smaller: "year"
  },
  // Elapsed units chain
  emillisecond: {
    magnitude: 1,
    smaller: void 0,
    greater: "esecond"
  },
  esecond: {
    magnitude: 3,
    smaller: "emillisecond",
    greater: "eminute"
  },
  eminute: {
    magnitude: 5,
    smaller: "esecond",
    greater: "ehour"
  },
  ehour: {
    magnitude: 7,
    smaller: "eminute",
    greater: "eday"
  },
  eday: {
    magnitude: 9,
    smaller: "ehour",
    greater: "eweek"
  },
  eweek: {
    magnitude: 11,
    smaller: "eday",
    greater: "emonth"
  },
  emonth: {
    magnitude: 12,
    smaller: "eweek",
    greater: "equarter"
  },
  equarter: {
    magnitude: 14,
    smaller: "emonth",
    greater: "eyear"
  },
  eyear: {
    magnitude: 16,
    smaller: "equarter",
    greater: "edecade"
  },
  edecade: {
    magnitude: 18,
    smaller: "eyear"
  }
};
var snapFns = {
  round(number, step = 1) {
    return Math.round(number / step) * step;
  },
  floor(number, step = 1) {
    return Math.floor(number / step) * step;
  },
  ceil(number, step = 1) {
    return Math.ceil(number / step) * step;
  }
};
defineUnitConversion("edecade", "millisecond", 3600 * 864e5);
defineUnitConversion("eyear", "millisecond", 360 * 864e5);
defineUnitConversion("equarter", "millisecond", 90 * 864e5);
defineUnitConversion("emonth", "millisecond", 30 * 864e5);
defineUnitConversion("eweek", "millisecond", 7 * 864e5);
defineUnitConversion("eday", "millisecond", 864e5);
defineUnitConversion("ehour", "millisecond", 36e5);
defineUnitConversion("eminute", "millisecond", 6e4);
defineUnitConversion("esecond", "millisecond", 1e3);
defineUnitConversion("emillisecond", "millisecond", 1e3);
var _DateHelper = class _DateHelper extends Localizable_default() {
  //region Parse & format
  /**
   * Get/set the default format used by `format()` and `parse()`. Defaults to `'YYYY-MM-DDTHH:mm:ssZ'`
   * (~ISO 8601 Date and time, `'1962-06-17T09:21:34Z'`).
   * @member {String}
   */
  static set defaultFormat(format2) {
    DH2._defaultFormat = format2;
  }
  static get defaultFormat() {
    return DH2._defaultFormat || "YYYY-MM-DDTHH:mm:ssZ";
  }
  /**
   * Get/set the default format used by `parse()`. Defaults to `'YYYY-MM-DDTHH:mm:ss.SSSZ'` or {@link #property-defaultFormat-static}
   * (~ISO 8601 Date and time, `'1962-06-17T09:21:34.123Z'`).
   * @member {String}
   */
  static set defaultParseFormat(parseFormat) {
    this._defaultParseFormat = parseFormat;
  }
  static get defaultParseFormat() {
    return this._defaultParseFormat || this._defaultFormat || "YYYY-MM-DDTHH:mm:ss.SSSZ";
  }
  static get today() {
    return _DateHelper.clearTime(/* @__PURE__ */ new Date(), false);
  }
  static get tomorrow() {
    return _DateHelper.add(_DateHelper.today, 1, "day");
  }
  static get yesterday() {
    return _DateHelper.add(_DateHelper.today, -1, "day");
  }
  static buildParser(format2) {
    const parts = format2.split(parserRegexp), parser = [];
    if (parts.length === 1 || localeStrRegExp.test(format2)) {
      return [];
    } else {
      parts.reduce((prev, curr, index, array) => {
        if (index !== 0 || curr !== "") {
          if (parserRegexp.test(curr)) {
            const localeParsers = this.localize("L{parsers}") || {}, fn2 = localeParsers[curr] || parsers[curr];
            if (curr === "Z" && index < array.length - 2) {
              throw new Error(`Invalid format ${format2} TimeZone (Z) must be last token`);
            }
            const parserObj = typeof fn2 === "function" || typeof fn2 === "string" ? fn2 : fn2.parser();
            if (typeof parserObj === "string") {
              const nestedParsers = DH2.buildParser(parserObj), lastItem = nestedParsers.pop();
              delete lastItem.last;
              parser.push(...nestedParsers);
              prev = lastItem;
            } else {
              prev.pattern = curr;
              prev.fn = parserObj;
            }
          } else {
            prev.splitter = curr;
            prev.escapedSplitter = StringHelper.escapeRegExp(curr);
            prev.canUseUnstrictSplitter = splitRegExp.test(curr);
            parser.push(prev);
            prev = {};
          }
        } else if (Object.prototype.hasOwnProperty.call(prev, "pattern")) {
          parser.push(prev);
        }
        return prev;
      }, {});
    }
    parser[parser.length - 1].last = true;
    parser.parseTimeZone = timeZoneFormatRe.test(format2);
    return parser;
  }
  /**
   * A utility function to create a sortable string key for the passed date or ms timestamp using the `'YYYY-MM-DD'`
   * format.
   * @param {Number|Date} ms The Date instance or ms timestamp to generate a key for
   * @returns {String} Date/timestamp as a string with `'YYYY-M-D'` format
   * @internal
   */
  static makeKey(ms) {
    if (ms.length === 10) {
      return ms;
    }
    if (ms.getTime) {
      ms = ms.getTime();
    }
    const cached = keyCache[Math.trunc(ms / keyDivisor)];
    if (cached) {
      return cached;
    }
    tempDate.setTime(ms);
    const month2 = tempDate.getMonth() + 1, date2 = tempDate.getDate();
    return keyCache[Math.trunc(ms / keyDivisor)] = `${tempDate.getFullYear()}-${month2 < 10 ? "0" + month2 : month2}-${date2 < 10 ? "0" + date2 : date2}`;
  }
  /**
   * A utility function to parse a sortable string to a date using the `'YYYY-MM-DD'` format.
   * @param {String} key The string to return a date for
   * @returns {Date} new Date instance
   * @internal
   */
  static parseKey(key) {
    const bc = (key == null ? void 0 : key[0]) === "-", date2 = DH2.parse(bc ? key.slice(1) : key, "YYYY-MM-DD");
    bc && date2.setFullYear(-date2.getFullYear());
    return date2;
  }
  /**
   * Returns a date created from the supplied string using the specified format. Will try to create even if format
   * is left out, by first using the default format (see {@link #property-defaultFormat-static}, by default
   * `YYYY-MM-DDTHH:mm:ssZ`) and then using `new Date(dateString)`.
   * Supported tokens:
   *
   * | Unit        | Token | Description                                      |
   * |-------------|-------|--------------------------------------------------|
   * | Year        | YYYY  | 4-digits year, like: 2018                        |
   * |             | Y     | numeric, any number of digits                    |
   * |             | YY    | < 68 -> 2000, > 68 -> 1900                       |
   * | Month       | MM    | 01 - 12                                          |
   * | Month       | MMM   | Short name of the month                          |
   * | Date        | DD    | 01 - 31                                          |
   * | Hour        | HH    | 00 - 23 or 1 - 12                                |
   * | Minute      | mm    | 00 - 59                                          |
   * | Second      | ss    | 00 - 59                                          |
   * | Millisecond | S     | 0 - 9 [000, 100, 200 .. 900 ]                    |
   * |             | SS    | 00 - 99 [000, 010, 020 .. 990 ]                  |
   * |             | SSS   | 000 - 999 [000, 001, 002 .. 999 ]                |
   * | AM/PM       | A     | AM or PM                                         |
   * |             | a     | am or pm                                         |
   * | TimeZone    | Z     | Z for UTC or +-HH:mm. Local timezone if left out |
   * | Predefined  | L     | Long date, MM/DD/YYYY                            |
   * |             | LT    | Long time, HH:mm A                               |
   *
   * Predefined formats and functions used to parse tokens can be localized, see for example the swedish locale
   * `SvSE.js`.
   *
   * NOTE: If no date parameters are provided then `Jan 01 2020` is used as a default date.
   *
   * {@note}Note that date strings without timezone information ('Z' or '+-HH:mm') will be in the local timezone. Eg.
   * '2024-05-14' -> local time, '2024-05-14Z' -> UTC{/@note}
   *
   * @param {String} dateString Date string
   * @param {String} [format] Date format (or {@link #property-defaultParseFormat-static} if left out)
   * @returns {Date} new Date instance parsed from the string
   * @category Parse & format
   */
  static parse(dateString, format2 = DH2.defaultParseFormat, strict = false) {
    if (dateString instanceof Date) {
      return dateString;
    }
    if (typeof dateString !== "string" || !dateString) {
      return null;
    }
    const config = {
      year: null,
      month: null,
      date: null,
      hours: null,
      minutes: null,
      seconds: null,
      milliseconds: null
    };
    format2 = format2.replace(msRegExp, msReplacer);
    let parser = parserCache[format2], result = null, timeZoneStr = null;
    if (!parser) {
      parser = parserCache[format2] = DH2.buildParser(format2);
    }
    if (dateString.includes("\u202F")) {
      dateString = dateString.replace(/\s/g, " ");
    }
    if (parser.parseTimeZone) {
      const timeZoneMatch = dateString.match(timeZoneRegExp);
      if (timeZoneMatch) {
        timeZoneStr = timeZoneMatch[0];
        dateString = dateString.slice(0, timeZoneMatch.index);
      }
    }
    parser.reduce((dateString2, parser2) => {
      var _a5;
      if (parser2.last) {
        dateString2 = timeZoneStr || dateString2;
        Objects.assign(config, parser2.fn(dateString2));
      } else {
        let splitAt;
        if (parser2.splitter === "T" && dateString2.indexOf("T") === -1) {
          splitAt = dateString2.indexOf(" ");
        } else {
          let splitter;
          if (!strict && parser2.canUseUnstrictSplitter) {
            splitter = splitRegExp;
          } else {
            splitter = parser2.escapedSplitter;
          }
          splitAt = splitter !== "" ? dateString2.search(splitter) : ((_a5 = parser2.pattern) == null ? void 0 : _a5.length) || -1;
        }
        let part, rest;
        if (splitAt === -1 || parser2.pattern === "SSS" && dateString2.match(/^\d+Z$/)) {
          const chunks = dateString2.split(/([Z\-+])/);
          if (chunks.length === 1) {
            part = dateString2;
            rest = "";
          } else {
            part = chunks[0];
            rest = `${chunks[1]}${chunks[2]}`;
          }
        } else {
          part = dateString2.substring(0, splitAt) || dateString2;
          rest = dateString2.substring(splitAt + parser2.splitter.length);
        }
        if (parser2.fn) {
          const res = parser2.fn(part);
          if (res) {
            Objects.assign(config, res);
          } else {
            rest = part + rest;
          }
        }
        return rest;
      }
    }, dateString);
    if (config.year && !config.date) {
      config.date = 1;
    }
    if (config.date > 31 || config.month > 12) {
      return null;
    }
    const date2 = DH2.create(config, strict, false);
    if (date2) {
      result = date2;
    } else if (!strict && dateString.length > 3) {
      result = new Date(dateString);
      if (result.getFullYear() < 1900) {
        result = null;
      }
    }
    return result;
  }
  /**
   * Creates a date from a date definition object. The object can have the following properties:
   * - year
   * - month
   * - date (day in month)
   * - hours
   * - minutes
   * - seconds
   * - milliseconds
   * - amPm : 'am' or 'pm', implies 12-hour clock
   * - timeZone : offset from UTC in minutes
   * @param {Object} definition
   * @param {Number} definition.year
   * @param {Number} [definition.month]
   * @param {Number} [definition.date]
   * @param {Number} [definition.hours]
   * @param {Number} [definition.minutes]
   * @param {Number} [definition.seconds]
   * @param {Number} [definition.milliseconds]
   * @param {Number} [definition.amPm]
   * @param {Number} [definition.timeZone]
   * @returns {Date} new Date instance
   * @category Parse & format
   */
  static create(definition, strict = false, clone = true) {
    const def = clone ? { ...definition } : definition;
    let invalid = isNaN(def.year) || strict && (isNaN(def.month) || isNaN(def.date)), useUTC = false;
    if (!invalid) {
      let allNull = true;
      for (let i = 0; i < dateProperties.length; i++) {
        const property = dateProperties[i];
        if (!(property in def) || isNaN(def[property])) {
          def[property] = 0;
        } else if (allNull) {
          allNull = def[property] === null;
        }
      }
      invalid = allNull;
    }
    if (invalid) {
      return null;
    }
    if (def.amPm === "am") {
      def.hours = def.hours % 12;
    } else if (def.amPm === "pm") {
      def.hours = def.hours % 12 + 12;
    }
    if ("timeZone" in def) {
      useUTC = true;
      def.minutes -= def.timeZone;
    }
    if (strict && (def.year == null || def.month == null || def.date == null)) {
      return null;
    }
    return useUTC ? new Date(
      Date.UTC(
        defaultValue(def.year, DEFAULT_YEAR),
        defaultValue(def.month, DEFAULT_MONTH),
        defaultValue(def.date, DEFAULT_DAY),
        def.hours,
        def.minutes,
        def.seconds,
        def.milliseconds
      )
    ) : new Date(
      defaultValue(def.year, DEFAULT_YEAR),
      defaultValue(def.month, DEFAULT_MONTH),
      defaultValue(def.date, DEFAULT_DAY),
      def.hours,
      def.minutes,
      def.seconds,
      def.milliseconds
    );
  }
  static toUTC(date2) {
    return new Date(Date.UTC(
      date2.getUTCFullYear(),
      date2.getUTCMonth(),
      date2.getUTCDate(),
      date2.getUTCHours(),
      date2.getUTCMinutes(),
      date2.getUTCSeconds(),
      date2.getUTCMilliseconds()
    ));
  }
  /**
   * Converts a date to string with the specified format. Formats heavily inspired by https://momentjs.com.
   * Available formats (input used for output below is `new Date(2018,8,9,18,7,8,145)`):
   *
   * | Unit                  | Token | Description & output                  |
   * |-----------------------|-------|---------------------------------------|
   * | Decade                | DC    | 2010s                                 |
   * | Year                  | YYYY  | 2018                                  |
   * |                       | YY    | 18                                    |
   * |                       | Y     | 2018                                  |
   * | Quarter               | Q     | 3                                     |
   * |                       | Qo    | 3rd                                   |
   * | Month                 | MMMM  | September                             |
   * |                       | MMM   | Sep                                   |
   * |                       | MM    | 09                                    |
   * |                       | Mo    | 9th                                   |
   * |                       | M     | 9                                     |
   * | Week (iso)            | WW    | 37 (2 digit, zero padded)             |
   * |                       | Wo    | 37th                                  |
   * |                       | W     | 37                                    |
   * |                       | WWp   | Week 37 (localized prefix, zero pad)  |
   * |                       | Wp    | Week 37 (localized prefix)            |
   * |                       | WWp0  | W37 (localized prefix)                |
   * |                       | Wp0   | W37 (localized prefix)                |
   * | Date                  | DDDD  | Day of year, 3 digits                 |
   * |                       | DDDo  | Day of year, ordinal                  |
   * |                       | DDD   | Day of year                           |
   * |                       | DD    | 09                                    |
   * |                       | Do    | 9th                                   |
   * |                       | D     | 9                                     |
   * | Weekday               | dddd  | Sunday                                |
   * |                       | ddd   | Sun                                   |
   * |                       | dd    | Su                                    |
   * |                       | d1    | S                                     |
   * |                       | do    | 0th                                   |
   * |                       | d     | 0                                     |
   * | Hour                  | HH    | 18 (00 - 23)                          |
   * |                       | H     | 18 (0 - 23)                           |
   * |                       | hh    | 06 (00 - 12)                          |
   * |                       | h     | 6 (0 - 12)                            |
   * |                       | KK    | 19 (01 - 24)                          |
   * |                       | K     | 19 (1 - 24)                           |
   * |                       | kk    | 06 or 18, locale determines           |
   * |                       | k     | 6 or 18, locale determines            |
   * | Minute                | mm    | 07                                    |
   * |                       | m     | 7                                     |
   * | Second                | ss    | 08                                    |
   * |                       | s     | 8                                     |
   * | Millisecond           | S     | 1 (100ms)                             |
   * |                       | SS    | 14 (140ms)                            |
   * |                       | SSS   | 145 (145ms)                           |
   * | AM/PM                 | A     | AM or PM                              |
   * |                       | a     | am or pm                              |
   * | Predefined            | LT    | H: 2-digit (2d), m: 2d                |
   * | (uses browser locale) | LTS   | H: 2d, m: 2d, s : 2d                  |
   * |                       | LST   | Depends on 12 or 24 hour clock        |
   * |                       |       | 12h, H : 1d, m : 0 or 2d              |
   * |                       |       | 24h, H : 2d, m : 2d                   |
   * |                       | L     | Y: numeric (n), M : 2d, D : 2d        |
   * |                       | l     | Y: n, M : n, D : n                    |
   * |                       | LL    | Y: n, M : long (l), D : n             |
   * |                       | ll    | Y: n, M : short (s), D : n            |
   * |                       | LLL   | Y: n, M : l, D : n, H: n, m: 2d       |
   * |                       | lll   | Y: n, M : s, D : n, H: n, m: 2d       |
   * |                       | LLLL  | Y: n, M : l, D : n, H: n, m: 2d, d: l |
   * |                       | llll  | Y: n, M : s, D : n, H: n, m: 2d, d: s |
   *
   * Some examples:
   *
   * ```javascript
   * DateHelper.format(new Date(2019, 7, 16), 'dddd') -> Friday
   * DateHelper.format(new Date(2019, 7, 16, 14, 27), 'HH:mm') --> 14:27
   * DateHelper.format(new Date(2019, 7, 16, 14, 27), 'L HH') --> 2019-07-16 14
   * ```
   *
   * Arbitrary text can be embedded in the format string by wrapping it with {}:
   *
   * ```javascript
   * DateHelper.format(new Date(2019, 7, 16), '{It is }dddd{, yay!}') -> It is Friday, yay!
   * ```
   *
   * @param {Date} date Date
   * @param {String|Function} [format] Desired format (uses `defaultFormat` if left out), or a function
   * which accepts a `Date` and returns the required formatted string.
   * @returns {String} Formatted string
   * @param {Date} [format.date]
   * @category Parse & format
   */
  static format(date2, format2 = DH2.defaultFormat) {
    if (!date2 || isNaN(date2)) {
      return null;
    }
    if (typeof format2 === "function") {
      return format2(date2);
    }
    let formatter = formatCache[format2], output = "";
    if (!formatter) {
      formatter = formatCache[format2] = [];
      for (let i = 0; i < format2.length; i++) {
        const formatMatch = format2.slice(i).match(formatRegexp), predefined = formatMatch == null ? void 0 : formatMatch[0];
        if (predefined) {
          const localeFormats = this.localize("L{formats}") || {}, fn2 = localeFormats[predefined] || formats[predefined];
          formatter.push(fn2);
          i += predefined.length - 1;
        } else if (format2[i] === "{") {
          const index = format2.indexOf("}", i + 1);
          if (index === -1) {
            formatter.push(format2.substr(i + 1));
            i = format2.length;
          } else {
            formatter.push(format2.substring(i + 1, index));
            i = index;
          }
        } else {
          formatter.push(format2[i]);
        }
      }
    }
    formatter.forEach((step) => {
      if (typeof step === "string") {
        output += step;
      } else {
        output += step(date2);
      }
    });
    return output;
  }
  /**
   * Formats a range of `dates` using the specified `format`. Because two dates are involved, the `format` specifier
   * uses the tokens `S{}` and `E{}`. The text contained between the `{}` is the {@link #function-format-static format}
   * for the start date or end date, respectively. Text not inside these tokens is retained verbatim.
   *
   * For example:
   *
   * ```javascript
   *  DateHelper.formatRange(dates, 'S{DD MMM YYYY} - E{DD MMM YYYY}');
   * ```
   *
   * The above will format `dates[0]` based on the `S{DD MMM YYYY}` segment and `dates[1] using `E{DD MMM YYYY}`. The
   * `' - '` between these will remain between the two formatted dates.
   *
   * @param {Date[]} dates An array of start date and end date (`[startDate, endDate]`)
   * @param {String} format The format specifier
   * @returns {String}
   */
  static formatRange(dates, format2) {
    return format2.replace(
      rangeFormatPartRe,
      (s, which, fmt) => _DateHelper.format(dates[which === "S" ? 0 : 1], fmt)
    );
  }
  /**
   * Converts the specified amount of desired unit into milliseconds. Can be called by only specifying a unit as the
   * first argument, it then uses `amount = 1`.
   *
   * For example:
   *
   * ```javascript
   * asMilliseconds('hour') == asMilliseconds(1, 'hour')
   * ```
   *
   * @param {Number|String} amount Amount, what of is decided by specifying unit (also takes a unit which implies an amount of 1)
   * @param {String} [unit] Time unit (s, hour, months etc.)
   * @returns {Number}
   * @category Parse & format
   */
  static asMilliseconds(amount, unit = null) {
    if (typeof amount === "string") {
      unit = amount;
      amount = 1;
    }
    return DH2.as("millisecond", amount, unit);
  }
  /**
   * Converts the passed Date to an accurate number of months passed since the epoch start.
   * @param {Date} time The Date to find the month value of
   * @returns {Number} The number of months since the system time epoch start. May be a fractional value
   */
  static asMonths(time) {
    const monthLength = DH2.as("ms", DH2.daysInMonth(time), "day"), fraction = (time.valueOf() - DH2.startOf(time, "month").valueOf()) / monthLength;
    return time.getYear() * 12 + time.getMonth() + fraction;
  }
  static monthsToDate(months) {
    const intMonths = Math.floor(months), fraction = months - intMonths, result = new Date(0, intMonths), msInMonth = DH2.as("ms", DH2.daysInMonth(result), "days");
    result.setTime(result.getTime() + fraction * msInMonth);
    return result;
  }
  /**
   * Converts a millisecond time delta to a human-readable form. For example `1000 * 60 * 60 * 50`
   * milliseconds would be rendered as `'2 days, 2 hours'`.
   * @param {Number} delta The millisecond delta value
   * @param {Object} [options] Formatting options
   * @param {Boolean} [options.abbrev] Pass `true` to use abbreviated unit names, eg. `'2d, 2h'` for the above example
   * @param {String} [options.precision] The minimum precision unit
   * @param {String} [options.separator] The separator to use between magnitude and unit
   * @param {String} [options.unitSeparator] The separator to use between each duration part, defaults to ", " eg. `'2d, 2h'`
   * @param {Boolean} [options.asString] Pass `false` to return the result as an array, eg ['2d', '2h'] for the above example
   * @returns {String} Formatted string
   * @category Parse & format
   */
  static formatDelta(delta, options) {
    let abbrev, unitName, unitSeparator = ", ";
    if (typeof options === "boolean") {
      abbrev = options;
    } else if (options) {
      abbrev = options.abbrev;
      unitSeparator = options.unitSeparator || unitSeparator;
    }
    const deltaObj = this.getDelta(delta, options), result = [], sep = (options == null ? void 0 : options.separator) || (abbrev ? "" : " ");
    for (unitName in deltaObj) {
      result.push(`${deltaObj[unitName]}${sep}${unitName}`);
    }
    return (options == null ? void 0 : options.asString) === false ? result : result.join(unitSeparator);
  }
  /**
   * Converts a millisecond time delta to an object structure. For example `1000 * 60 * 60 * 50`
   * milliseconds the result would be as:
   *
   * ```javascript
   * {
   *     day  : 2,
   *     hour : 2
   * }
   * ```
   *
   * @param {Number} delta The millisecond delta value
   * @param {Object} [options] Formatting options
   * @param {Boolean} [options.abbrev] Pass `true` to use abbreviated unit names, eg `{ d: 2, h: 2 }` for the above example
   * @param {String} [options.precision] The minimum precision unit
   * @param {Boolean} [options.ignoreLocale] Pass true to return unlocalized unit name. Requires `abbrev` to be false
   * @param {String} [options.maxUnit] Name of the maximum unit in the output. e.g. if you pass `day` then you'll get
   * `{ h: 25 }` instead of `{ d: 1, h: 1 }`
   * @returns {Object} The object with the values for each unit
   */
  static getDelta(delta, options) {
    let abbrev, d, done, precision, unitName, maxUnit, ignoreLocale;
    if (typeof options === "boolean") {
      abbrev = options;
    } else if (options) {
      abbrev = options.abbrev;
      precision = DH2.normalizeUnit(options.precision);
      maxUnit = options.maxUnit;
      ignoreLocale = !abbrev && options.ignoreLocale;
    }
    const result = {}, getUnit = abbrev ? DH2.getShortNameOfUnit : DH2.getLocalizedNameOfUnit;
    const units = maxUnit ? deltaUnits.slice(deltaUnits.indexOf(maxUnit)) : deltaUnits;
    for (unitName of units) {
      d = DH2.as(unitName, delta);
      done = precision === unitName;
      d = Math[done ? "round" : "floor"](d);
      if (d || done && !result.length) {
        result[ignoreLocale ? unitName : getUnit.call(DH2, unitName, d !== 1)] = d;
        delta -= DH2.as("ms", d, unitName);
      }
      if (done || !delta) {
        break;
      }
    }
    return result;
  }
  /**
   * Converts the specified amount of one unit (`fromUnit`) into an amount of another unit (`toUnit`).
   * @param {String} toUnit The name of units to convert to, eg: `'ms'`
   * @param {Number|String} amount The time to convert. Either the magnitude number form or a duration string such as '2d'
   * @param {String} [fromUnit='ms'] If the amount was passed as a number, the units to use to convert from
   * @returns {Number}
   * @category Parse & format
   */
  static as(toUnit, amount, fromUnit = "ms") {
    if (typeof amount === "string") {
      amount = DH2.parseDuration(amount);
    }
    if (typeof amount === "object") {
      fromUnit = amount.unit;
      amount = amount.magnitude;
    }
    if (toUnit === fromUnit || !fromUnit || !toUnit) {
      return amount;
    }
    toUnit = DH2.normalizeUnit(toUnit);
    fromUnit = DH2.normalizeUnit(fromUnit);
    if (toUnit === fromUnit) {
      return amount;
    } else if (unitChain[fromUnit].magnitude > unitChain[toUnit].magnitude) {
      return amount * Math.abs(validConversions[fromUnit][toUnit]);
    } else {
      return amount / Math.abs(validConversions[toUnit][fromUnit]);
    }
  }
  static formatContainsHourInfo(format2) {
    const stripEscapeRe = /(\\.)/g, hourInfoRe = /([HhKkmSsAa]|LT|L{3,}|l{3,})/;
    return hourInfoRe.test(format2.replace(stripEscapeRe, ""));
  }
  /**
   * Returns `true` for 24-hour format.
   * @param {String} format Date format
   * @returns {Boolean} `true` for 24-hour format
   * @category Parse & format
   */
  static is24HourFormat(format2) {
    return DH2.format(DH2.getTime(13, 0, 0), format2).includes("13");
  }
  //endregion
  //region Manipulate
  /**
   * Add days, hours etc. to a date. Always clones the date, original will be left unaffected.
   * @param {Date|String} date Original date
   * @param {Number|String|Core.data.Duration|DurationConfig} amount Amount of days, hours etc. or a string representation of a duration
   * as accepted by {@link #function-parseDuration-static} or an object with `{ magnitude, unit }` properties
   * @param {DurationUnitShort} [unit='ms'] Unit for amount
   * @privateparam {Boolean} [clone=true] Pass `false` to affect the original
   * @returns {Date} New calculated date
   * @category Manipulate
   */
  static add(date2, amount, unit = "ms", clone = true) {
    let d;
    if (typeof date2 === "string") {
      d = DH2.parse(date2);
    } else if (clone) {
      d = new Date(date2.getTime());
    } else {
      d = date2;
    }
    if (typeof amount === "string") {
      const duration = _DateHelper.parseDuration(amount);
      amount = duration.magnitude;
      unit = duration.unit;
    } else if (amount && typeof amount === "object") {
      unit = amount.unit;
      amount = amount.magnitude;
    }
    if (!unit || amount === 0) {
      return d;
    }
    unit = DH2.normalizeUnit(unit);
    switch (unit) {
      case "millisecond":
        d.setTime(d.getTime() + amount);
        break;
      case "second":
        d.setTime(d.getTime() + amount * 1e3);
        break;
      case "minute":
        d.setTime(d.getTime() + amount * 6e4);
        break;
      case "hour":
        d.setTime(d.getTime() + amount * 36e5);
        break;
      case "day":
        if (amount % 1 === 0) {
          d.setDate(d.getDate() + amount);
          if (d.getHours() === 23 && date2.getHours() === 0) {
            d.setHours(d.getHours() + 1);
          }
        } else {
          d.setTime(d.getTime() + amount * 864e5);
        }
        break;
      case "week":
        d.setDate(d.getDate() + amount * 7);
        break;
      case "month": {
        let day2 = d.getDate();
        if (day2 > 28) {
          day2 = Math.min(day2, DH2.getLastDateOfMonth(DH2.add(DH2.getFirstDateOfMonth(d), amount, "month")).getDate());
        }
        d.setDate(day2);
        d.setMonth(d.getMonth() + amount);
        break;
      }
      case "quarter":
        DH2.add(d, amount * 3, "month", false);
        break;
      case "year":
        DH2.add(d, amount * 12, "month", false);
        break;
      case "decade":
        d.setFullYear(d.getFullYear() + amount * 10);
        break;
    }
    return d;
  }
  /**
   * Calculates the difference between two dates, in the specified unit.
   * @param {Date} start First date
   * @param {Date} end Second date
   * @param {DurationUnitShort} [unit='ms'] Unit to calculate difference in
   * @param {Boolean} [fractional=true] Specify false to round result
   * @returns {Number} Difference in the specified unit
   * @category Manipulate
   */
  static diff(start, end, unit = "ms", fractional = true) {
    unit = DH2.normalizeUnit(unit);
    if (!start || !end)
      return 0;
    let amount;
    switch (unit) {
      case "year":
        amount = DH2.diff(start, end, "month") / 12;
        break;
      case "quarter":
        amount = DH2.diff(start, end, "month") / 3;
        break;
      case "month":
        amount = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
        if (amount === 0 && fractional) {
          amount = DH2.diff(start, end, "day", fractional) / DH2.daysInMonth(start);
        }
        break;
      case "week":
        amount = DH2.diff(start, end, "day") / 7;
        break;
      case "day": {
        const dstDiff = start.getTimezoneOffset() - end.getTimezoneOffset();
        amount = (end - start + dstDiff * 60 * 1e3) / 864e5;
        break;
      }
      case "hour":
        amount = (end - start) / 36e5;
        break;
      case "minute":
        amount = (end - start) / 6e4;
        break;
      case "second":
        amount = (end - start) / 1e3;
        break;
      case "millisecond":
        amount = end - start;
        break;
    }
    return fractional ? amount : Math.round(amount);
  }
  /**
   * Sets the date to the start of the specified unit, by default returning a clone of the date instead of changing it
   * in place.
   * @param {Date} date Original date
   * @param {String} [unit='day'] Start of this unit, `'day'`, `'month'` etc
   * @param {Boolean} [clone=true] Manipulate a copy of the date
   * @param {Number} [weekStartDay] The first day of week, `0-6` (Sunday-Saturday). Defaults to the {@link #property-weekStartDay-static}
   * @returns {Date} Manipulated date
   * @category Manipulate
   */
  static startOf(date2, unit = "day", clone = true, weekStartDay = DH2.weekStartDay) {
    if (!date2) {
      return null;
    }
    unit = DH2.normalizeUnit(unit);
    if (clone) {
      date2 = DH2.clone(date2);
    }
    switch (unit) {
      case "year":
        date2.setMonth(0, 1);
        date2.setHours(0, 0, 0, 0);
        return date2;
      case "quarter":
        date2.setMonth((DH2.get(date2, "quarter") - 1) * 3, 1);
        date2.setHours(0, 0, 0, 0);
        return date2;
      case "month":
        date2.setDate(1);
        date2.setHours(0, 0, 0, 0);
        return date2;
      case "week": {
        const dayOfWeek = date2.getDay();
        let delta = dayOfWeek - weekStartDay;
        if (delta < 0) {
          delta += 7;
        }
        date2.setDate(date2.getDate() - delta);
        date2.setHours(0, 0, 0, 0);
        return date2;
      }
      case "day":
        date2.setHours(0, 0, 0, 0);
        return date2;
      case "hour":
        date2.getMinutes() > 0 && date2.setMinutes(0);
      case "minute":
        date2.getSeconds() > 0 && date2.setSeconds(0);
      case "second":
        date2.getMilliseconds() > 0 && date2.setMilliseconds(0);
      case "millisecond":
        return date2;
    }
  }
  /**
   * Returns the end point of the passed date, that is 00:00:00 of the day after the passed date.
   * @param {Date} date The date to return the end point of
   * @returns {Date} Manipulated date
   */
  static endOf(date2) {
    return new Date(date2.getFullYear(), date2.getMonth(), date2.getDate() + 1);
  }
  /**
   * Creates a clone of the specified date
   * @param {Date} date Original date
   * @returns {Date} Cloned date
   * @category Manipulate
   */
  static clone(date2) {
    return new Date(date2.getTime());
  }
  /**
   * Removes time from a date (same as calling {@link #function-startOf-static startOf(date)}).
   * @param {Date} date Date to remove time from
   * @param {Boolean} [clone=true] Manipulate a copy of the date
   * @returns {Date} Manipulated date
   * @category Manipulate
   */
  static clearTime(date2, clone = true) {
    if (!date2) {
      return null;
    }
    if (clone) {
      date2 = new Date(date2.getTime());
    }
    date2.setHours(0, 0, 0, 0);
    return date2;
  }
  static midnight(date2, inclusive) {
    let ret = DH2.clearTime(date2);
    if (inclusive && ret < date2) {
      ret = DH2.add(ret, 1, "d");
    }
    return ret;
  }
  static isMidnight(date2) {
    return DH2.midnight(date2).getTime() === date2.getTime();
  }
  /**
   * Returns the elapsed milliseconds from the start of the specified date.
   * @param {Date} date Date to remove date from
   * @param {DurationUnitShort} [unit='ms'] The time unit to return
   * @returns {Number} The elapsed milliseconds from the start of the specified date
   * @category Manipulate
   */
  static getTimeOfDay(date2, unit = "ms") {
    const t = date2.getHours() * validConversions.hour.millisecond + date2.getMinutes() * validConversions.minute.millisecond + date2.getSeconds() * validConversions.second.millisecond + date2.getMilliseconds();
    return unit === "ms" ? t : DH2.as(unit, t, "ms");
  }
  /**
   * Sets a part of a date (in place).
   * @param {Date} date Date to manipulate
   * @param {String|Object} unit Part of date to set, for example `'minute'`. Or an object like `{ second: 1, minute: 1 }`
   * @param {Number} amount Value to set
   * @returns {Date} Passed date instance modified according to the arguments
   * @category Manipulate
   */
  static set(date2, unit, amount) {
    if (!unit) {
      return date2;
    }
    if (typeof unit === "string") {
      switch (DH2.normalizeUnit(unit)) {
        case "millisecond":
          if (amount !== 0 || date2.getMilliseconds() > 0) {
            date2.setMilliseconds(amount);
          }
          break;
        case "second":
          if (amount !== 0 || date2.getSeconds() > 0) {
            date2.setSeconds(amount);
          }
          break;
        case "minute":
          if (amount !== 0 || date2.getMinutes() > 0) {
            date2.setMinutes(amount);
          }
          break;
        case "hour":
          date2.setHours(amount);
          break;
        case "day":
        case "date":
          date2.setDate(amount);
          break;
        case "week":
          throw new Error("week not implemented");
        case "month":
          date2.setMonth(amount);
          break;
        case "quarter":
          date2.setDate(1);
          date2.setMonth((amount - 1) * 3);
          break;
        case "year":
          date2.setFullYear(amount);
          break;
      }
    } else {
      Object.entries(unit).sort((a, b) => {
        const unitA = a[0], unitB = b[0], unitAData = unitChain[unitA === "date" ? "day" : unitA], unitBData = unitChain[unitB === "date" ? "day" : unitB];
        return unitAData.magnitude - unitBData.magnitude;
      }).forEach(([unit2, amount2]) => {
        DH2.set(date2, unit2, amount2);
      });
    }
    return date2;
  }
  static setDateToMidday(date2, clone = true) {
    return DH2.set(DH2.clearTime(date2, clone), "hour", 12);
  }
  /**
   * Constrains the date within a min and a max date.
   * @param {Date} date The date to constrain
   * @param {Date} [min] Min date
   * @param {Date} [max] Max date
   * @returns {Date} The constrained date
   * @category Manipulate
   */
  static constrain(date2, min4, max3) {
    if (min4 != null) {
      date2 = DH2.max(date2, min4);
    }
    return max3 == null ? date2 : DH2.min(date2, max3);
  }
  /**
   * Returns time with default year, month, and day (Jan 1, 2020).
   * @param {Number|Date} hours Hours value or the full date to extract the time of
   * @param {Number} [minutes=0] Minutes value
   * @param {Number} [seconds=0] Seconds value
   * @param {Number} [ms=0] Milliseconds value
   * @returns {Date} A new default date with the time extracted from the given date or from the time values provided individually
   * @category Manipulate
   */
  static getTime(hours2, minutes = 0, seconds2 = 0, ms = 0) {
    if (hours2 instanceof Date) {
      ms = hours2.getMilliseconds();
      seconds2 = hours2.getSeconds();
      minutes = hours2.getMinutes();
      hours2 = hours2.getHours();
    }
    return new Date(DEFAULT_YEAR, DEFAULT_MONTH, DEFAULT_DAY, hours2, minutes, seconds2, ms);
  }
  /**
   * Copies hours, minutes, seconds, milliseconds from one date to another.
   *
   * @param {Date} targetDate The target date
   * @param {Date} sourceDate The source date
   * @returns {Date} The adjusted target date
   * @category Manipulate
   * @static
   */
  static copyTimeValues(targetDate, sourceDate) {
    targetDate.setHours(sourceDate.getHours());
    targetDate.setMinutes(sourceDate.getMinutes());
    targetDate.setSeconds(sourceDate.getSeconds());
    targetDate.setMilliseconds(sourceDate.getMilliseconds());
    return targetDate;
  }
  /**
   * Combines the hours, minutes, seconds, milliseconds from one date with the year, month, and date of another.
   * The result is returned in a new `Date` instance
   *
   * @param {Date} date The `Date` object providing the day, month, and year components.
   * @param {Date} time The `Date` object providing the hours, minutes, seconds, and milliseconds components.
   * @returns {Date} A new `Date` combining the date from `date` and the time from `time`.
   * @category Manipulate
   * @static
   * @internal
   */
  static combineDateAndTime(date2, time) {
    return _DateHelper.copyTimeValues(new Date(date2), time);
  }
  //endregion
  //region Comparison
  static get isDSTEnabled() {
    const year = (/* @__PURE__ */ new Date()).getFullYear(), jan = new Date(year, 0, 1), jul = new Date(year, 6, 1);
    return jan.getTimezoneOffset() !== jul.getTimezoneOffset();
  }
  static isDST(date2) {
    const year = date2.getFullYear(), jan = new Date(year, 0, 1), jul = new Date(year, 6, 1);
    return date2.getTimezoneOffset() < Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
  }
  /**
   * Determines if a date is today's date.
   * @param {Date} date The date to check
   * @param {String} [timeZone] The time zone to use for the comparison. If not specified, the local time zone is used:
   *
   * ```javascript
   * // Check if a date is today in the local time zone
   * DateHelper.isToday(new Date());  // true if today
   * // Check if a date is today in a specific time zone
   * DateHelper.isToday(new Date(), 'America/New_York');  // true if today in New York
   * ```
   * @returns {Boolean} `true` if the date is today, otherwise false
   * @category Comparison
   */
  static isToday(date2, timeZone) {
    return DH2.isEqual(DH2.clearTime(date2), timeZone ? DH2.clearTime(Helpers_default.TimeZone.toTimeZone(DH2.today, timeZone)) : DH2.today);
  }
  /**
   * Determines if a date precedes another.
   * @param {Date} first First date
   * @param {Date} second Second date
   * @returns {Boolean} `true` if first precedes second, otherwise false
   * @category Comparison
   */
  static isBefore(first, second) {
    return first < second;
  }
  /**
   * Determines if a date succeeds another.
   * @param {Date} first First date
   * @param {Date} second Second date
   * @returns {Boolean} `true` if first succeeds second, otherwise false
   * @category Comparison
   */
  static isAfter(first, second) {
    return first > second;
  }
  /**
   * Checks if two dates are equal.
   * @param {Date} first First date
   * @param {Date} second Second date
   * @param {String} [unit] Unit to calculate difference in. If not given, the comparison will be done up to a millisecond
   * @returns {Boolean} `true` if the dates are equal
   * @category Comparison
   */
  static isEqual(first, second, unit = null) {
    if (unit === null) {
      return first && second && first.getTime() === second.getTime();
    }
    return DH2.startOf(first, unit) - DH2.startOf(second, unit) === 0;
  }
  /**
   * Compares two dates using the specified precision.
   * @param {Date} first First date
   * @param {Date} second Second date
   * @param {String} [unit] Unit to calculate difference in. If not given, the comparison will be done up to a millisecond
   * @returns {Number} `0` = equal, `-1` = first before second, `1` = first after second
   * @category Comparison
   */
  static compare(first, second, unit = null) {
    if (unit) {
      first = DH2.startOf(first, unit);
      second = DH2.startOf(second, unit);
    }
    if (first < second)
      return -1;
    if (first > second)
      return 1;
    return 0;
  }
  /**
   * Coerces the passed Date between the passed minimum and maximum values.
   * @param {Date} date The date to clamp between the `min` and `max`
   * @param {Date} min The minimum Date
   * @param {Date} max The maximum Date
   * @returns {Date} If the passed `date` is valid, a *new* Date object which is clamped between the `min` and `max`
   */
  static clamp(date2, min4, max3) {
    if (!isNaN(date2)) {
      if (min4 != null) {
        date2 = Math.max(date2, min4);
      }
      if (max3 != null) {
        date2 = Math.min(date2, max3);
      }
      return new Date(date2);
    }
  }
  static isSameDate(first, second) {
    return DH2.compare(first, second, "d") === 0;
  }
  static isSameTime(first, second) {
    return first.getHours() === second.getHours() && first.getMinutes() === second.getMinutes() && first.getSeconds() === second.getSeconds() && first.getMilliseconds() === second.getMilliseconds();
  }
  /**
   * Checks if date is the start of specified unit.
   * @param {Date} date Date
   * @param {DurationUnit} unit Time unit
   * @returns {Boolean} `true` if date is the start of specified unit
   * @category Comparison
   */
  static isStartOf(date2, unit) {
    return DH2.isEqual(date2, DH2.startOf(date2, unit));
  }
  /**
   * Checks if this date is `>= start` and `< end`.
   * @param {Date} date The source date
   * @param {Date} start Start date
   * @param {Date} end End date
   * @returns {Boolean} `true` if this date falls on or between the given start and end dates
   * @category Comparison
   */
  static betweenLesser(date2, start, end) {
    return start.getTime() <= date2.getTime() && date2.getTime() < end.getTime();
  }
  /**
   * Checks if this date is `>= start` and `<= end`.
   * @param {Date} date The source date
   * @param {Date} start Start date
   * @param {Date} end End date
   * @returns {Boolean} `true` if this date falls on or between the given start and end dates
   * @category Comparison
   */
  static betweenLesserEqual(date2, start, end) {
    return start.getTime() <= date2.getTime() && date2.getTime() <= end.getTime();
  }
  /**
   * Returns `true` if date ranges intersect.
   * @param {Date} date1Start Start date of first span
   * @param {Date} date1End End date of first span
   * @param {Date} date2Start Start date of second span
   * @param {Date} date2End End date of second span
   * @returns {Boolean} Returns `true` if dates intersect
   * @category Comparison
   */
  static intersectSpans(date1Start, date1End, date2Start, date2End) {
    return DH2.betweenLesser(date1Start, date2Start, date2End) || DH2.betweenLesser(date2Start, date1Start, date1End);
  }
  /**
   * Compare two units. Returns `1` if first param is a greater unit than second param, `-1` if the opposite is true or `0` if they're equal.
   * @param {DurationUnit} unit1 The 1st unit
   * @param {DurationUnit} unit2 The 2nd unit
   * @returns {Number} Returns `1` if first param is a greater unit than second param, `-1` if the opposite is true or `0` if they're equal
   * @category Comparison
   */
  static compareUnits(unit1, unit2) {
    const unit1Data = unitChain[DH2.normalizeUnit(unit1)], unit2Data = unitChain[DH2.normalizeUnit(unit2)];
    return Math.sign(((unit1Data == null ? void 0 : unit1Data.magnitude) || 0) - ((unit2Data == null ? void 0 : unit2Data.magnitude) || 0));
  }
  /**
   * Returns `true` if the first time span completely 'covers' the second time span.
   *
   * ```javascript
   * DateHelper.timeSpanContains(
   *     new Date(2010, 1, 2),
   *     new Date(2010, 1, 5),
   *     new Date(2010, 1, 3),
   *     new Date(2010, 1, 4)
   * ) ==> true
   * DateHelper.timeSpanContains(
   *   new Date(2010, 1, 2),
   *   new Date(2010, 1, 5),
   *   new Date(2010, 1, 3),
   *   new Date(2010, 1, 6)
   * ) ==> false
   * ```
   *
   * @param {Date} spanStart The start date for initial time span
   * @param {Date} spanEnd The end date for initial time span
   * @param {Date} otherSpanStart The start date for the 2nd time span
   * @param {Date} otherSpanEnd The end date for the 2nd time span
   * @returns {Boolean} `true` if the first time span completely 'covers' the second time span
   * @category Comparison
   */
  static timeSpanContains(spanStart, spanEnd, otherSpanStart, otherSpanEnd) {
    return otherSpanStart - spanStart >= 0 && spanEnd - otherSpanEnd >= 0;
  }
  //endregion
  //region Query
  /**
   * Get the local AM indicator string.
   * @property {string}
   * @readonly
   */
  static get amIndicator() {
    return amIndicator;
  }
  /**
   * Get the local PM indicator string.
   * @property {string}
   * @readonly
   */
  static get pmIndicator() {
    return pmIndicator;
  }
  /**
   * Get the first day of week, 0-6 (Sunday-Saturday).
   * This is determined by the current locale's `DateHelper.weekStartDay` parameter.
   * @property {Number}
   * @readonly
   */
  static get weekStartDay() {
    if (DH2._weekStartDay == null) {
      DH2._weekStartDay = this.localize("L{weekStartDay}") || 0;
    }
    return DH2._weekStartDay;
  }
  /**
   * Get non-working days as an object where keys are day indices, 0-6 (Sunday-Saturday), and the value is `true`.
   * This is determined by the current locale's `DateHelper.nonWorkingDays` parameter.
   *
   * For example:
   * ```javascript
   * {
   *     0 : true, // Sunday
   *     6 : true  // Saturday
   * }
   * ```
   *
   * @property {Object<Number,Boolean>}
   * @readonly
   */
  static get nonWorkingDays() {
    return { ...this.localize("L{nonWorkingDays}") };
  }
  /**
   * Get non-working days as an array of day indices, 0-6 (Sunday-Saturday).
   * This is determined by the current locale's `DateHelper.nonWorkingDays` parameter.
   *
   * For example:
   *
   * ```javascript
   * [ 0, 6 ] // Sunday & Saturday
   * ```
   *
   * @property {Number[]}
   * @readonly
   * @internal
   */
  static get nonWorkingDaysAsArray() {
    return Object.keys(this.nonWorkingDays).map(Number);
  }
  /**
   * Get weekend days as an object where keys are day indices, 0-6 (Sunday-Saturday), and the value is `true`.
   * Weekends are days which are declared as weekend days by the selected country and defined by the current locale's
   * `DateHelper.weekends` parameter.
   * To get non-working days see {@link #property-nonWorkingDays-static}.
   *
   * For example:
   * ```javascript
   * {
   *     0 : true, // Sunday
   *     6 : true  // Saturday
   * }
   * ```
   * @property {Object<Number,Boolean>}
   * @readonly
   * @internal
   */
  static get weekends() {
    return { ...this.localize("L{weekends}") };
  }
  /**
   * Get the specified part of a date.
   * @param {Date} date
   * @param {DurationUnit} unit Part of date, hour, minute etc.
   * @returns {Number} The requested part of the specified date
   * @category Query
   */
  static get(date2, unit) {
    switch (DH2.normalizeUnit(unit)) {
      case "millisecond":
        return date2.getMilliseconds();
      case "second":
        return date2.getSeconds();
      case "minute":
        return date2.getMinutes();
      case "hour":
        return date2.getHours();
      case "date":
      case "day":
        return date2.getDate();
      case "week":
        return formats.W(date2);
      case "month":
        return date2.getMonth();
      case "quarter":
        return Math.floor(date2.getMonth() / 3) + 1;
      case "year":
        return date2.getFullYear();
    }
    return null;
  }
  /**
   * Get number of days in the current year for the supplied date.
   * @param {Date} date Date to check
   * @returns {Number} Days in year
   * @category Query
   * @internal
   */
  static daysInYear(date2) {
    const fullYear = date2.getFullYear(), duration = new Date(fullYear + 1, 0, 1) - new Date(fullYear, 0, 1);
    return this.as("day", duration);
  }
  /**
   * Get number of days in the current month for the supplied date.
   * @param {Date} date Date which month should be checked
   * @returns {Number} Days in month
   * @category Query
   */
  static daysInMonth(date2) {
    return 32 - new Date(date2.getFullYear(), date2.getMonth(), 32).getDate();
  }
  /**
   * Get number of hours in the current day for the supplied date.
   * @param {Date} date Date to check
   * @returns {Number} Hours in day
   * @category Query
   * @internal
   */
  static hoursInDay(date2) {
    const fullYear = date2.getFullYear(), month2 = date2.getMonth(), day2 = date2.getDate(), duration = new Date(fullYear, month2, day2 + 1) - new Date(fullYear, month2, day2);
    return this.as("hour", duration);
  }
  /**
   * Converts unit related to the date to actual amount of milliseconds in it. Takes into account leap years and
   * different duration of months.
   * @param {Date} date Date
   * @param {DurationUnit} unit Time unit
   * @returns {Number} Returns amount of milliseconds
   * @internal
   */
  static getNormalizedUnitDuration(date2, unit) {
    let result;
    switch (unit) {
      case "month":
        result = DH2.asMilliseconds(DH2.daysInMonth(date2), "day");
        break;
      case "year":
        result = DH2.asMilliseconds(DH2.daysInYear(date2), "day");
        break;
      case "day":
        result = DH2.asMilliseconds(DH2.hoursInDay(date2), "hour");
        break;
      default:
        result = DH2.asMilliseconds(unit);
    }
    return result;
  }
  /**
   * Get the first date of the month for the supplied date.
   * @param {Date} date Date
   * @returns {Date} New Date instance
   * @category Query
   */
  static getFirstDateOfMonth(date2) {
    return new Date(date2.getFullYear(), date2.getMonth(), 1);
  }
  /**
   * Get the last date of the month for the supplied date.
   * @param {Date} date Date
   * @returns {Date} New Date instance
   * @category Query
   */
  static getLastDateOfMonth(date2) {
    return new Date(date2.getFullYear(), date2.getMonth() + 1, 0);
  }
  /**
   * Get the earliest of two dates.
   * @param {Date} first First date
   * @param {Date} second Second date
   * @returns {Date} Earliest date
   * @category Query
   */
  static min(first, second) {
    return first.getTime() < second.getTime() ? first : second;
  }
  /**
   * Get the latest of two dates.
   * @param {Date} first First date
   * @param {Date} second Second date
   * @returns {Date} Latest date
   * @category Query
   */
  static max(first, second) {
    return first.getTime() > second.getTime() ? first : second;
  }
  /**
   * Get an incremented date. Incrementation based on specified unit and optional amount.
   * @param {Date} date Date
   * @param {DurationUnit} unit Time unit
   * @param {Number} [increment=1] Increment amount
   * @param {Number} [weekStartDay] Will default to what is set in locale
   * @returns {Date} New Date instance
   * @category Query
   */
  static getNext(date2, unit, increment = 1, weekStartDay = DH2.weekStartDay) {
    if (unit === "week") {
      const dt = DH2.clone(date2), day2 = dt.getDay();
      DH2.startOf(dt, "day", false);
      DH2.add(dt, weekStartDay - day2 + 7 * (increment - (weekStartDay <= day2 ? 0 : 1)), "day", false);
      if (dt.getDay() !== weekStartDay) {
        DH2.add(dt, 1, "hour");
      }
      return dt;
    }
    return DH2.startOf(DH2.add(date2, increment, unit), unit, false);
  }
  /**
   * Checks if date object is valid.
   *
   * For example:
   *
   * ```javascript
   * date = new Date('foo')
   * date instanceof Date // true
   * date.toString() // Invalid Date
   * isNaN(date) // true
   * DateHelper.isValidDate(date) // false
   *
   * date = new Date()
   * date instanceof Date // true
   * date.toString() // Mon Jan 13 2020 18:27:38 GMT+0300 (GMT+03:00)
   * isNaN(date) // false
   * DateHelper.isValidDate(date) // true
   * ```
   *
   * @param {Date} date Date
   * @returns {Boolean} `true` if date object is valid
   */
  static isValidDate(date2) {
    return DH2.isDate(date2) && !isNaN(date2);
  }
  /**
   * Checks if value is a date object. Allows to recognize date object even from another context,
   * like the top frame when used in an iframe.
   *
   * @param {*} value Value to check
   * @returns {Boolean} `true` if value is a date object
   */
  static isDate(value2) {
    return value2 && toString3.call(value2) === DATE_TYPE2;
  }
  /**
   * Get the start of the next day.
   * @param {Date} date Date
   * @param {Boolean} [clone=false] Clone date
   * @param {Boolean} [noNeedToClearTime=false] Flag to not clear time from the result
   * @returns {Date} Passed Date or new Date instance, depending on the `clone` flag
   * @category Query
   */
  static getStartOfNextDay(date2, clone = false, noNeedToClearTime = false) {
    let nextDay = DH2.add(noNeedToClearTime ? date2 : DH2.clearTime(date2, clone), 1, "day");
    if (nextDay.getDate() === date2.getDate()) {
      const offsetNextDay = DH2.add(DH2.clearTime(date2, clone), 2, "day").getTimezoneOffset(), offsetDate = date2.getTimezoneOffset();
      nextDay = DH2.add(nextDay, offsetDate - offsetNextDay, "minute");
    }
    return nextDay;
  }
  /**
   * Get the end of previous day.
   * @param {Date} date Date
   * @param {Boolean} [noNeedToClearTime=false] Flag to not clear time from the result
   * @returns {Date} New Date instance
   * @category Query
   */
  static getEndOfPreviousDay(date2, noNeedToClearTime = false) {
    const dateOnly = noNeedToClearTime ? date2 : DH2.clearTime(date2, true);
    if (dateOnly - date2) {
      return dateOnly;
    } else {
      return DH2.add(dateOnly, -1, "day");
    }
  }
  /**
   * Returns a string describing the specified week. For example, `'39, September 2020'` or `'40, Sep - Oct 2020'`.
   * @param {Date} startDate Start date
   * @param {Date} [endDate] End date
   * @returns {String} String describing the specified week
   * @internal
   */
  static getWeekDescription(startDate, endDate = startDate) {
    const monthDesc = startDate.getMonth() === endDate.getMonth() ? _DateHelper.format(startDate, "MMMM") : `${_DateHelper.format(startDate, "MMM")} - ${_DateHelper.format(endDate, "MMM")}`, week = _DateHelper.getWeekNumber(startDate);
    return `${week[1]}, ${monthDesc} ${week[0]}`;
  }
  /**
   * Get week number for the date.
   * @param {Date} date The date
   * @param {Number} [weekStartDay] The first day of week, 0-6 (Sunday-Saturday). Defaults to the {@link #property-weekStartDay-static}
   * @returns {Number[]} year and week number
   * @category Query
   */
  static getWeekNumber(date2, weekStartDay = _DateHelper.weekStartDay) {
    const jan01 = new Date(date2.getFullYear(), 0, 1), dec31 = new Date(date2.getFullYear(), 11, 31), firstDay = normalizeDay(jan01.getDay() - weekStartDay), lastDay = normalizeDay(dec31.getDay() - weekStartDay), dayNumber = getDayDiff(date2, jan01);
    let weekNumber;
    if (firstDay < 4) {
      weekNumber = Math.floor((dayNumber + firstDay - 1) / 7) + 1;
    } else {
      weekNumber = Math.floor((dayNumber + firstDay - 1) / 7);
    }
    if (weekNumber) {
      let year = date2.getFullYear();
      if (weekNumber === 53 && lastDay < 3) {
        year++;
        weekNumber = 1;
      }
      return [year, weekNumber];
    }
    const lastWeekOfLastYear = _DateHelper.getWeekNumber(new Date(date2.getFullYear() - 1, 11, 31))[1];
    return [date2.getFullYear() - 1, lastWeekOfLastYear];
  }
  static getDateFromISOWeek(isoYear, isoWeek, weekday = 1) {
    const simple = new Date(isoYear, 0, 1 + (isoWeek - 1) * 7), dayOfWeek = simple.getDay(), isoDay = dayOfWeek === 0 ? 7 : dayOfWeek, diff = weekday - isoDay;
    simple.setDate(simple.getDate() + diff);
    return simple;
  }
  //endregion
  //region Unit helpers
  /**
   * Turns `(10, 'day')` into `'10 days'` etc.
   * @param {Number} count Amount of unit
   * @param {DurationUnit} unit Unit, will be normalized (days, d -> day etc.)
   * @returns {String} Amount formatted to string
   * @category Unit helpers
   */
  static formatCount(count, unit) {
    unit = DH2.normalizeUnit(unit);
    if (count !== 1)
      unit += "s";
    return count + " " + unit;
  }
  /**
   * Get the ratio between two units ( year, month -> 1/12 ).
   * @param {DurationUnit} baseUnit Base time unit
   * @param {DurationUnit} unit Time unit
   * @param {Boolean} [acceptEstimate=false] If `true`, process negative values of validConversions
   * @returns {Number} Ratio
   * @category Unit helpers
   */
  static getUnitToBaseUnitRatio(baseUnit, unit, acceptEstimate = false) {
    baseUnit = DH2.normalizeUnit(baseUnit);
    unit = DH2.normalizeUnit(unit);
    if (baseUnit === unit)
      return 1;
    if (validConversions[baseUnit] && validConversions[baseUnit][unit] && (acceptEstimate || validConversions[baseUnit][unit] > 0)) {
      return 1 / DH2.as(unit, 1, baseUnit);
    }
    if (validConversions[unit] && validConversions[unit][baseUnit] && (acceptEstimate || validConversions[unit][baseUnit] > 0)) {
      return DH2.as(baseUnit, 1, unit);
    }
    return -1;
  }
  /**
   * Returns a localized abbreviated form of the name of the duration unit.
   * For example in the `EN` locale, for `'qrt'` it will return `'q'`.
   * @param {DurationUnit} unit Duration unit
   * @returns {String} Localized abbreviated form of the name of the duration unit
   * @category Unit helpers
   */
  static getShortNameOfUnit(unit) {
    unit = DH2.parseTimeUnit(unit);
    return DH2.unitLookup[unit].abbrev;
  }
  /**
   * Returns a localized full name of the duration unit.
   *
   * For example in the `EN` locale, for `'d'` it will return either
   * `'day'` or `'days'`, depending on the `plural` argument
   *
   * Preserves casing of first letter.
   *
   * @static
   * @param {DurationUnit} unit Time unit
   * @param {Boolean} [plural=false] Whether to return a plural name or singular
   * @returns {String} Localized full name of the duration unit
   * @category Unit helpers
   */
  static getLocalizedNameOfUnit(unit, plural = false) {
    const capitalize = unit.charAt(0) === unit.charAt(0).toUpperCase();
    unit = DH2.normalizeUnit(unit);
    unit = DH2.parseTimeUnit(unit);
    unit = DH2.unitLookup[unit][plural ? "plural" : "single"];
    if (capitalize) {
      unit = StringHelper.capitalize(unit);
    }
    return unit;
  }
  /**
   * Normalizes a unit for easier usage in conditionals. For example `'year'`, `'years'`, `'y'` -> `'year'`.
   * @param {DurationUnit} unit Time unit
   * @returns {String} Normalized unit name
   * @category Unit helpers
   */
  static normalizeUnit(unit) {
    if (!unit) {
      return null;
    }
    const unitLower = unit.toLowerCase();
    if (unitLower === "date") {
      return unitLower;
    }
    return canonicalUnitNames.includes(unitLower) ? unitLower : normalizedUnits[unit] || normalizedUnits[unitLower];
  }
  static getUnitByName(name) {
    return DH2.normalizeUnit(name) || DH2.normalizeUnit(DH2.parseTimeUnit(name));
  }
  /**
   * Returns a duration of the timeframe in the given unit.
   * @param {Date} start The start date of the timeframe
   * @param {Date} end The end date of the timeframe
   * @param {DurationUnit} unit Duration unit
   * @privateparam {Boolean} [doNotRound]
   * @returns {Number} The duration in the units
   * @category Unit helpers
   * @ignore
   */
  static getDurationInUnit(start, end, unit, doNotRound) {
    return DH2.diff(start, end, unit, doNotRound);
  }
  /**
   * Checks if two date units align.
   * @private
   * @param {String} majorUnit Major time unit
   * @param {String} minorUnit Minor time unit
   * @returns {Boolean} `true` if two date units align
   * @category Unit helpers
   */
  static doesUnitsAlign(majorUnit, minorUnit) {
    return !(majorUnit !== minorUnit && minorUnit === "week");
  }
  static getSmallerUnit(unit) {
    var _a5, _b2;
    unit = DH2.normalizeUnit(unit);
    const smallerUnitName = (_a5 = unitChain[unit]) == null ? void 0 : _a5.smaller;
    return canonicalUnitNames[(_b2 = unitChain[smallerUnitName]) == null ? void 0 : _b2.magnitude] || null;
  }
  static getLargerUnit(unit) {
    var _a5, _b2;
    unit = DH2.normalizeUnit(unit);
    const largerUnitName = (_a5 = unitChain[unit]) == null ? void 0 : _a5.greater;
    return canonicalUnitNames[(_b2 = unitChain[largerUnitName]) == null ? void 0 : _b2.magnitude] || null;
  }
  /**
   * Rounds the passed Date value to the nearest `increment` value.
   *
   * Optionally may round relative to a certain base time point.
   *
   * For example `DH.round(new Date('2020-01-01T09:35'), '30 min', new Date('2020-01-01T09:15'))`
   * would round to 9:45 because that's the nearest integer number of 30 minute increments
   * from the base.
   *
   * Note that `base` is ignored when rounding to weeks. The configured {@link #property-weekStartDay-static}
   * dictates what the base of a week is.
   *
   * @param {Date} time The time to round
   * @param {String|Number} increment A millisecond value by which to round the time
   * May be specified in string form eg: `'15 minutes'`
   * @param {Date} [base] The start from which to apply the rounding
   * @param {Number} [weekStartDay] Will default to what is set in locale
   * @returns {Date} New Date instance
   */
  static round(time, increment, base, weekStartDay) {
    return DH2.snap("round", time, increment, base, weekStartDay);
  }
  /**
   * Floor the passed Date value to the nearest `increment` value.
   *
   * Optionally may floor relative to a certain base time point.
   *
   * For example `DH.floor(new Date('2020-01-01T09:35'), '30 min', new Date('2020-01-01T09:15'))`
   * would floor to 9:15 because that's the closest lower integer number of 30 minute increments
   * from the base.
   *
   * Note that `base` is ignored when flooring to weeks. The configured {@link #property-weekStartDay-static}
   * dictates what the base of a week is.
   *
   * @param {Date} time The time to floor
   * @param {String|Number|DurationConfig|Object} increment A numeric millisecond value by which to floor the time.
   * or a duration in string form eg `'30 min'` or object form : `{unit: 'minute', magnitude: 30}`
   * or `{unit: 'minute', increment: 30}`
   * @param {Date} [base] The start from which to apply the flooring
   * @param {Number} [weekStartDay] Will default to what is set in locale
   * @returns {Date} New Date instance
   */
  static floor(time, increment, base, weekStartDay) {
    return DH2.snap("floor", time, increment, base, weekStartDay);
  }
  /**
   * Ceils the passed Date value to the nearest `increment` value.
   *
   * Optionally may ceil relative to a certain base time point.
   *
   * For example `DH.ceil(new Date('2020-01-01T09:35'), '30 min', new Date('2020-01-01T09:15'))`
   * would ceil to 9:45 because that's the closest higher integer number of 30 minute increments
   * from the base.
   *
   * Note that `base` is ignored when ceiling to weeks. Use weekStartDay argument which default to the configured
   * {@link #property-weekStartDay-static} dictates what the base of a week is
   *
   * @param {Date} time The time to ceil
   * @param {String|Number|DurationConfig|Object} increment A numeric millisecond value by which to ceil the time
   * or a duration in string form eg `'30 min'` or object form : `{unit: 'minute', magnitude: 30}`
   * or `{unit: 'minute', increment: 30}`
   * @param {Date} [base] The start from which to apply the ceiling
   * @param {Number} [weekStartDay] Will default to what is set in locale
   * @returns {Date} New Date instance
   */
  static ceil(time, increment, base, weekStartDay) {
    return DH2.snap("ceil", time, increment, base, weekStartDay);
  }
  /**
   * Implementation for round, floor and ceil.
   * @internal
   */
  static snap(operation, time, increment, base, weekStartDay = DH2.weekStartDay) {
    const snapFn = snapFns[operation];
    if (typeof increment === "string") {
      increment = DH2.parseDuration(increment);
    }
    if (Objects.isObject(increment)) {
      const magnitude = increment.magnitude || increment.increment;
      switch (increment.unit) {
        case "week": {
          if (!base) {
            const weekDay = time.getDay();
            base = DH2.add(
              DH2.clearTime(time),
              weekDay >= weekStartDay ? weekStartDay - weekDay : -(weekDay - weekStartDay + 7),
              "day"
            );
          }
          return DH2[operation](time, `${magnitude * 7} days`, base);
        }
        case "month": {
          time = DH2.asMonths(time);
          let resultMonths;
          if (base) {
            base = DH2.asMonths(base);
            resultMonths = base + snapFn(time - base, magnitude);
          } else {
            resultMonths = snapFn(time, magnitude);
          }
          return DH2.monthsToDate(resultMonths);
        }
        case "quarter":
          return DH2[operation](time, `${magnitude * 3} months`, base);
        case "year":
          return DH2[operation](time, `${magnitude * 12} months`, base);
        case "decade":
          return DH2[operation](time, `${magnitude * 10} years`, base);
      }
      increment = DH2.as("ms", magnitude, increment.unit);
    }
    const timeUtcOffset = time.getTimezoneOffset() * 1e3 * 60;
    if (base) {
      const baseUtcOffset = base.getTimezoneOffset() * 1e3 * 60, diff = time - base + baseUtcOffset - timeUtcOffset, snappedDate = new Date(base.getTime() + snapFn(diff, increment)), dstChange = baseUtcOffset - snappedDate.getTimezoneOffset() * 1e3 * 60;
      if (dstChange) {
        DH2.add(snappedDate, -dstChange, "ms", false);
      }
      return new Date(snappedDate);
    } else {
      return new Date(snapFn(time.valueOf() - timeUtcOffset, increment) + timeUtcOffset);
    }
  }
  //endregion
  //region Date picker format
  /**
   * Parses a typed duration value according to locale rules.
   *
   * The value is taken to be a string consisting of the numeric magnitude and the units:
   * - The numeric magnitude can be either an integer or a float value. Both `','` and `'.'` are valid decimal separators.
   * - The units may be a recognised unit abbreviation of this locale or the full local unit name.
   *
   * For example:
   * `'2d'`, `'2 d'`, `'2 day'`, `'2 days'` will be turned into `{ magnitude : 2, unit : 'day' }`
   * `'2.5d'`, `'2,5 d'`, `'2.5 day'`, `'2,5 days'` will be turned into `{ magnitude : 2.5, unit : 'day' }`
   *
   * **NOTE:** Doesn't work with complex values like `'2 days, 2 hours'`
   *
   * @param {String} value The value to parse
   * @param {Boolean} [allowDecimals=true] Decimals are allowed in the magnitude
   * @param {DurationUnit|String} [defaultUnit] Default unit to use if only magnitude passed
   * @returns {DurationConfig} If successfully parsed, the result contains two properties, `magnitude` being a number, and
   * `unit` being the canonical unit name, *NOT* a localized name. If parsing was unsuccessful, `null` is returned
   * @category Parse & format
   */
  static parseDuration(value2, allowDecimals = true, defaultUnit) {
    var _a5;
    const durationRegEx = allowDecimals ? withDecimalsDurationRegex : noDecimalsDurationRegex, match = durationRegEx.exec(value2);
    if (value2 == null || !match) {
      return null;
    }
    const magnitude = parseNumber((_a5 = match[1]) == null ? void 0 : _a5.replace(",", ".")), unit = DH2.parseTimeUnit(match[2]) || defaultUnit;
    if (!unit) {
      return null;
    }
    return {
      magnitude,
      unit
    };
  }
  /**
   * Parses a typed unit name, for example `'ms'` or `'hr'` or `'yr'` into the
   * canonical form of the unit name which may be passed to {@link #function-add-static}
   * or {@link #function-diff-static}.
   * @param {DurationUnit|DurationUnitShort|String} unitName Time unit name
   * @category Parse & format
   */
  static parseTimeUnit(unitName) {
    const unitMatch = unitName == null ? null : DH2.durationRegEx.exec(unitName) || DH2.durationRegEx.exec(unitName.toLowerCase());
    if (!unitMatch) {
      return null;
    }
    for (let unitOrdinal = 0; unitOrdinal < canonicalUnitNames.length; unitOrdinal++) {
      if (unitMatch[unitOrdinal + 1]) {
        return canonicalUnitNames[unitOrdinal];
      }
    }
  }
  //endregion
  //region Internal
  static getGMTOffset(date2 = /* @__PURE__ */ new Date()) {
    if (!date2) {
      return;
    }
    const offsetInMinutes = date2.getTimezoneOffset();
    if (!offsetInMinutes)
      return "Z";
    return (offsetInMinutes > 0 ? "-" : "+") + Math.abs(Math.trunc(offsetInMinutes / 60)).toString().padStart(2, "0") + ":" + Math.abs(offsetInMinutes % 60).toString().padStart(2, "0");
  }
  static fillDayNames() {
    const tempDate3 = /* @__PURE__ */ new Date("2000-01-01T12:00:00"), dayNames = DH2._dayNames || [], dayShortNames = DH2._dayShortNames || [];
    dayNames.length = 0;
    dayShortNames.length = 0;
    for (let day2 = 2; day2 < 9; day2++) {
      tempDate3.setDate(day2);
      dayNames.push(DH2.format(tempDate3, "dddd"));
      dayShortNames.push(DH2.format(tempDate3, "ddd"));
    }
    DH2._dayNames = dayNames;
    DH2._dayShortNames = dayShortNames;
  }
  static getDayNames() {
    return DH2._dayNames;
  }
  static getDayName(day2) {
    return DH2._dayNames[day2];
  }
  static getDayShortNames() {
    return DH2._dayShortNames;
  }
  static getDayShortName(day2) {
    return DH2._dayShortNames[day2];
  }
  static fillMonthNames() {
    const tempDate3 = /* @__PURE__ */ new Date("2000-01-15T12:00:00"), monthNames = DH2._monthNames || [], monthShortNames = DH2._monthShortNames || [], monthNamesIndex = {}, monthShortNamesIndex = {};
    monthNames.length = 0;
    monthShortNames.length = 0;
    for (let month2 = 0; month2 < 12; month2++) {
      tempDate3.setMonth(month2);
      const monthName = DH2.format(tempDate3, "MMMM");
      monthNames.push(monthName);
      const monthShortName = DH2.format(tempDate3, "MMM");
      monthShortNames.push(monthShortName);
      monthNamesIndex[monthName.toLowerCase()] = { name: monthName, value: month2 };
      monthShortNamesIndex[monthShortName.toLowerCase()] = { name: monthShortName, value: month2 };
    }
    DH2._monthNames = monthNames;
    DH2._monthShortNames = monthShortNames;
    DH2._monthNamesIndex = monthNamesIndex;
    DH2._monthShortNamesIndex = monthShortNamesIndex;
  }
  static getMonthShortNames() {
    return DH2._monthShortNames;
  }
  static getMonthShortName(month2) {
    return DH2._monthShortNames[month2];
  }
  static getMonthNames() {
    return DH2._monthNames;
  }
  static getMonthName(month2) {
    return DH2._monthNames[month2];
  }
  static set locale(name) {
    locale2 = name;
    intlFormatterCache = {};
    formatCache = {};
    formatRedirects = {};
  }
  static get locale() {
    return locale2;
  }
  static setupDurationRegEx(unitNames = [], unitAbbreviations = []) {
    const me = this, unitLookup = {};
    let unitAbbrRegEx = "";
    for (let i = 0; i < unitAbbreviations.length; i++) {
      const abbreviations = unitAbbreviations[i], unitNamesCfg = unitNames[i];
      unitNamesCfg.canonicalUnitName = canonicalUnitNames[i];
      unitLookup[unitNamesCfg.single] = unitLookup[unitNamesCfg.single.toUpperCase()] = unitLookup[unitNamesCfg.canonicalUnitName] = unitLookup[unitNamesCfg.canonicalUnitName.toUpperCase()] = unitNamesCfg;
      unitAbbrRegEx += `${i ? "|" : ""}(`;
      for (let j = 0; j < abbreviations.length; j++) {
        unitAbbrRegEx += `${abbreviations[j]}|`;
      }
      locale2 = me.localize("L{locale}") || "en-US";
      if (locale2 !== "en-US") {
        const canonicalAbbreviations = canonicalUnitAbbreviations[i];
        for (let j = 0; j < canonicalAbbreviations.length; j++) {
          unitAbbrRegEx += `${canonicalAbbreviations[j]}|`;
        }
      }
      unitAbbrRegEx += `${unitNamesCfg.single}|${unitNamesCfg.plural}|${unitNamesCfg.canonicalUnitName}|${unitNamesCfg.canonicalUnitName}s)`;
    }
    me.unitLookup = unitLookup;
    me.durationRegEx = new RegExp(`^(?:${unitAbbrRegEx})$`);
  }
  static applyLocale() {
    const me = this, unitAbbreviations = me.localize("L{unitAbbreviations}") || [], unitNames = me.unitNames = me.localize("L{unitNames}"), d = new Date(2020, 6, 15, 6), tzo = Math.abs(d.getTimezoneOffset() % 60);
    if (unitNames === "unitNames") {
      return;
    }
    locale2 = me.localize("L{locale}") || "en-US";
    const ampmFormatter = intlFormatterCache.a || (intlFormatterCache.a = new Intl.DateTimeFormat(locale2, {
      hour: "numeric",
      hourCycle: "h12"
    }));
    amIndicator = ampmFormatter.formatToParts(d)[2].value;
    pmIndicator = ampmFormatter.formatToParts(new Date(2020, 6, 15, 18))[2].value;
    if (locale2 === "en-US") {
      ordinalSuffix = enOrdinalSuffix;
    } else {
      ordinalSuffix = me.localize("L{ordinalSuffix}") || ordinalSuffix;
    }
    formatCache = {};
    formatRedirects = {};
    parserCache = {};
    intlFormatterCache = {};
    keyCache = {};
    keyDivisor = (!tzo || tzo > 30 ? 60 - tzo : tzo) * 6e4;
    DH2._weekStartDay = null;
    DH2.setupDurationRegEx(unitNames, unitAbbreviations);
    DH2.fillDayNames();
    DH2.fillMonthNames();
  }
  //endregion
};
__publicField(_DateHelper, "MS_PER_DAY", MS_PER_HOUR * 24);
__publicField(_DateHelper, "$name", "DateHelper");
var DateHelper = _DateHelper;
Helpers_default.register(DateHelper);
var DH2 = DateHelper;
DH2.useIntlFormat = useIntlFormat;
LocaleManager_default.ion({
  locale: "applyLocale",
  prio: 1e3,
  thisObj: DH2
});
if (LocaleManager_default.locale) {
  DH2.applyLocale();
}
var DateSet = class _DateSet extends Set {
  get isDateSet() {
    return true;
  }
  add(d) {
    d = DateHelper.makeKey(d);
    if (!this.has(d)) {
      this.generation = (this.generation || 0) + 1;
    }
    return super.add(d);
  }
  delete(d) {
    d = DateHelper.makeKey(d);
    if (this.has(d)) {
      this.generation++;
    }
    return super.delete(d);
  }
  has(d) {
    return super.has(DateHelper.makeKey(d));
  }
  clear() {
    if (this.size) {
      this.generation++;
    }
    return super.clear();
  }
  equals(other) {
    Array.isArray(other) && (other = new _DateSet(other));
    return other.size === this.size && [...this].every((s) => other.has(s));
  }
  get dates() {
    return [...this].sort().map((k) => DateHelper.parseKey(k));
  }
};
DateHelper.DateSet = DateSet;
DateHelper._$name = "DateHelper";

// ../Core/lib/Core/mixin/Delayable.js
var { defineProperty: defineProperty3 } = Reflect;
var performance2;
if (BrowserHelper.isBrowserEnv) {
  performance2 = globalThis.performance;
} else {
  performance2 = {
    now() {
      return (/* @__PURE__ */ new Date()).getTime();
    }
  };
}
var globalDelays = null;
if (VersionHelper.isTestEnv) {
  const bryntum2 = globalThis.bryntum || (globalThis.bryntum = {});
  globalDelays = bryntum2.globalDelays = {
    timeouts: /* @__PURE__ */ new Map(),
    intervals: /* @__PURE__ */ new Map(),
    animationFrames: /* @__PURE__ */ new Map(),
    idleCallbacks: /* @__PURE__ */ new Map(),
    isEmpty(includeIntervals = false) {
      return globalDelays.timeouts.size + globalDelays.animationFrames.size + globalDelays.idleCallbacks.size + (includeIntervals ? globalDelays.intervals.size : 0) === 0;
    },
    /**
     * Returns filtered delays array
     * @param {Object} options
     * @param {String[]} [options.ignoreTimeouts] array of delays names to ignore
     * @param {Number} [options.maxDelay] maximum delay in milliseconds. Timeouts with bigger delay will be filtered out
     * @param {Boolean} [options.includeIntervals] include intervals
     * @returns {Object[]} array of filtered delays
     * @internal
     */
    getFiltered({ ignoreTimeouts = [], maxDelay = 5e3, includeIntervals = false }) {
      const result = [], scopes = ["timeouts", "animationFrames", "idleCallbacks"];
      if (includeIntervals) {
        scopes.push("intervals");
      }
      for (const scope of scopes) {
        const map2 = globalDelays[scope];
        for (const [, entry] of map2.entries()) {
          if (!ignoreTimeouts.includes(entry.name) && (!Number.isInteger(entry.delay) || entry.delay < maxDelay)) {
            result.push(entry);
          }
        }
      }
      return result;
    }
  };
}
var asapPromise = Promise.resolve();
var emptyArray2 = Object.freeze([]);
var emptyFn3 = () => {
};
var emptyObject5 = Object.freeze({});
var queueMicrotask = globalThis.queueMicrotask;
var makeInvoker = (me, fn2, wrapFn, options) => {
  const named = typeof fn2 === "string", appendArgs = (options == null ? void 0 : options.appendArgs) || emptyArray2, invoker = () => {
    wrapFn.timerId = null;
    wrapFn.lastCallTime = performance2.now();
    const args = wrapFn.args;
    wrapFn.args = null;
    if (named) {
      me[fn2](...args, ...appendArgs);
    } else {
      fn2.call(me, ...args, ...appendArgs);
    }
    wrapFn.called = true;
    ++wrapFn.calls;
  };
  me = (options == null ? void 0 : options.thisObj) || me;
  wrapFn.lastCallTime = -9e9;
  wrapFn.calls = 0;
  wrapFn.suspended = options == null ? void 0 : options.suspended;
  wrapFn.invoker = invoker;
  invoker.wrapFn = wrapFn;
  invoker.fn = fn2;
  invoker.this = me;
  return invoker;
};
var decorateWrapFn = (me, wrapFn, cancelFn = "clearTimeout") => {
  wrapFn.cancel = () => {
    if (wrapFn.isPending) {
      me[cancelFn](wrapFn.timerId);
      wrapFn.args = wrapFn.timerId = null;
    }
  };
  wrapFn.flush = () => {
    if (wrapFn.isPending) {
      me[cancelFn](wrapFn.timerId);
      wrapFn.timerId = null;
      wrapFn.invoker();
    }
  };
  wrapFn.now = (...args) => {
    wrapFn.cancel();
    wrapFn.args = args;
    wrapFn.invoker();
  };
  wrapFn.resume = (all) => {
    const n = wrapFn.suspended;
    wrapFn.suspended = all || n < 1 ? 0 : n - 1;
  };
  wrapFn.suspend = () => {
    ++wrapFn.suspended;
  };
  wrapFn.immediate = false;
  wrapFn.suspended = wrapFn.suspended ? 1 : 0;
  wrapFn.timerId = null;
  defineProperty3(wrapFn, "isPending", {
    get() {
      return wrapFn.timerId !== null;
    }
  });
  return wrapFn;
};
var Delayable_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    static get declarable() {
      return [
        /**
         * This class property returns an object that specifies methods to wrap with configurable timer behaviors.
         *
         * It is used like so:
         * ```javascript
         *  class Foo extends Base.mixin(Delayable) {
         *      static get delayable() {
         *          return {
         *              expensiveMethod : 500
         *          };
         *      }
         *
         *      expensiveMethod() {
         *          this.things();
         *          this.moreThings();
         *          this.evenMoreThings();
         *      }
         *  }
         * ```
         * With the above in place, consider:
         * ```javascript
         *  let instance = new Foo();
         *
         *  instance.expensiveMethod();
         * ```
         * Instead of the above code immediately calling the `expensiveMethod()`, it will start a timer that will
         * invoke the method 500ms later. Because `expensiveMethod()` is an instance method, each instance of `Foo`
         * will have its own timer.
         *
         * NOTE: Only instance methods are currently supported (i.e., only non-`static` methods).
         *
         * #### Options
         * The value of each key configures how the method will be scheduled. If the value is a number, it is
         * promoted to a config object of `type='buffer'` as in the following:
         * ```javascript
         *  class Foo extends Base.mixin(Delayable) {
         *      static get delayable() {
         *          return {
         *              expensiveMethod : {
         *                  type  : 'buffer',
         *                  delay : 500
         *              }
         *          };
         *      }
         *  }
         * ```
         * The `type` property of the config object must be one of three values. Other options can be provided
         * depending on the `type`:
         *
         *  - `buffer`<br>
         *    Other options:
         *     - `delay` (Number) : The number of milliseconds to wait before calling the underlying method. A
         *       value of 0 is equivalent to setting `immediate: true`.
         *     - `immediate` (Boolean) : Set to `true` to call immediately (effectively disabling the buffer).
         *  - `raf` (short for "request animation frame")<br>
         *  - `idle` (short for "request idle callback") __Not available on Safari__ <br>
         *    Other options:
         *     - `cancelOutstanding` (Boolean) : Set to `true` to cancel any pending animation frame requests and
         *       schedule a new one on each call.
         *     - `immediate` (Boolean) : Set to `true` to call immediately.
         *  - `throttle`<br>
         *    Other options:
         *     - `delay` (Number) : The number of milliseconds to wait after each execution before another
         *       execution takes place. A value of 0 is equivalent to setting `immediate: true`.
         *     - `immediate` (Boolean) : Set to `true` to call immediately (effectively disabling the throttle).
         *
         * While `immediate: true` can be specified at the class level, it is more typical to set it on the
         * instance's method as described below.
         *
         * #### Delayable Method API
         * Delayable methods have a consistent API to manage their scheduling. This API is added to the methods
         * themselves.
         *
         * For example:
         * ```javascript
         *  let instance = new Foo();
         *
         *  instance.expensiveMethod();         // schedule a call in 500ms
         *  instance.expensiveMethod.isPending; // true
         *  instance.expensiveMethod.cancel();
         *  instance.expensiveMethod.flush();
         *  instance.expensiveMethod.now();
         *
         *  instance.expensiveMethod.delay = 10;
         *  instance.expensiveMethod();         // schedule a call in 10ms
         * ```
         *
         * ##### `isPending` (Boolean, readonly)
         * This boolean property will be `true` if a call has been scheduled, and false otherwise.
         *
         * ##### `cancel()`
         * Cancels a pending call if one has been scheduled. Otherwise this method does nothing.
         *
         * ##### `flush()`
         * Cancels the timer and causes the pending call to execute immediately. If there is no pending call, this
         * method does nothing.
         *
         * ##### `now()`
         * Cancels the timer (if one is pending) and executes the method immediately. If there is no pending call,
         * this method will still call the underlying method.
         *
         * @static
         * @member {Object<String,'raf'|Number|DelayableConfig>} delayable
         * @internal
         */
        "delayable"
      ];
    }
    doDestroy() {
      super.doDestroy();
      this.clearDelayables();
    }
    clearDelayables() {
      const me = this, [idleCallbackIds, animationFrameIds, intervalIds, timeoutMap, timeoutIds, microtasksPending] = [
        me.idleCallbackIds,
        me.animationFrameIds,
        me.intervalIds,
        me.timeoutMap,
        me.timeoutIds,
        me.microtasksPending
      ];
      me.idleCallbackIds = me.animationFrameIds = me.intervalIds = me.timeoutMap = me.timeoutIds = me.microtasksPending = null;
      timeoutIds == null ? void 0 : timeoutIds.forEach((fn2, id2) => {
        if (typeof fn2 === "function") {
          fn2();
        }
        clearTimeout(id2);
        globalDelays == null ? void 0 : globalDelays.timeouts.delete(id2);
      });
      timeoutMap == null ? void 0 : timeoutMap.forEach((name, id2) => clearTimeout(id2));
      intervalIds == null ? void 0 : intervalIds.forEach((id2) => {
        clearInterval(id2);
        globalDelays == null ? void 0 : globalDelays.intervals.delete(id2);
      });
      animationFrameIds == null ? void 0 : animationFrameIds.forEach((id2) => {
        cancelAnimationFrame(id2);
        globalDelays == null ? void 0 : globalDelays.animationFrames.delete(id2);
      });
      idleCallbackIds == null ? void 0 : idleCallbackIds.forEach((id2) => {
        cancelIdleCallback(id2);
        globalDelays == null ? void 0 : globalDelays.idleCallbacks.delete(id2);
      });
      microtasksPending == null ? void 0 : microtasksPending.forEach((taskDef) => taskDef.cancel());
    }
    /**
     * Check if a named timeout is active
     * @param name
     * @internal
     */
    hasTimeout(name) {
      var _a6;
      return Boolean((_a6 = this.timeoutMap) == null ? void 0 : _a6.has(name));
    }
    /**
     * Returns a function that when called will schedule a call to `fn` via {@link #function-queueMicrotask}.
     *
     * @param {Function|String} fn The function to call. If this is a string, it is looked up as a method on `this`
     * instance (or `options.thisObj` instead, if provided).
     * @param {Boolean|Object} [options] An options object.
     * @param {Array} [options.appendArgs] The argument list to append to those passed to the function.
     * @param {Object} [options.thisObj] The `this` reference for the function.
     * @returns {Function}
     * @internal
     */
    asap(fn2, options) {
      const me = this, asapWrapFn = (...params) => {
        if (!asapWrapFn.suspended) {
          asapWrapFn.called = false;
          asapWrapFn.args = params;
          if (asapWrapFn.immediate) {
            invoker();
          } else if (!asapWrapFn.isPending) {
            asapWrapFn.timerId = me.queueMicrotask(invoker);
          }
        }
      }, invoker = makeInvoker(me, fn2, asapWrapFn, options);
      return decorateWrapFn(me, asapWrapFn, "cancelMicrotask");
    }
    /**
     * Equivalent to the native [`queueMicrotask`](https://developer.mozilla.org/en-US/docs/Web/API/queueMicrotask), but
     * will be cancelled automatically on `destroy`. When `queueMicrotask` is not available, the provided `fn` is
     * called using `Promise.resolve().then(fn)`, which behaves similarly.
     *
     * Returns a function that when called will cancel the impending call. This function can also be viewed as a timer
     * id that can be passed to {@link #function-cancelMicrotask}
     *
     * @param {Function|String} fn The function to call, or name of function in this object to call.
     * @param {Array} [args] The arguments to pass.
     * @param {Object} [options] An object containing the details about that function, and the time delay.
     * @param {Array} [options.args] The arguments to pass if
     * @param {Boolean} [options.runOnDestroy] Pass `true` if this function should be executed if the Delayable instance
     * is destroyed while function is scheduled.
     * @param {Boolean} [options.cancelOutstanding] Pass `true` to cancel any outstanding invocation of the passed function.
     * @returns {Function}
     * @internal
     */
    queueMicrotask(fn2, args, options) {
      let key, taskDef;
      if (!options && args && !Array.isArray(args)) {
        options = args;
        args = null;
      }
      options = options || emptyObject5;
      args = args || options.args || emptyArray2;
      const me = this, string2 = typeof fn2 === "string", name = string2 ? fn2 : fn2.name, pending = me.microtasksPending || (me.microtasksPending = /* @__PURE__ */ new Map()), cancel = () => pending.delete(key), wrapFn = () => {
        if (taskDef === pending.get(key)) {
          pending.delete(key);
          if (string2) {
            !me.isDestroyed && me[fn2](...args);
          } else {
            fn2.apply(me, args);
          }
        }
      };
      if (me.isDestroying && !options.runOnDestroy) {
        return emptyFn3;
      }
      key = options.cancelOutstanding ? name || fn2 : cancel;
      taskDef = { key, name, fn: fn2, args, options, cancel, wrapFn };
      cancel.taskDef = taskDef;
      queueMicrotask ? queueMicrotask(wrapFn) : asapPromise.then(wrapFn);
      pending.set(key, taskDef);
      return cancel;
    }
    /**
     * Given the return value from a call to {@link #function-queueMicrotask}, cancels the pending call. This method is
     * provided for symmetry with other timer functions, such as {@link #function-setTimeout}.
     *
     * @param {Function} task The value returned by `queueMicrotask`.
     * @internal
     */
    cancelMicrotask(task) {
      task == null ? void 0 : task();
    }
    /**
     * Same as native setTimeout, but will be cleared automatically on destroy. If a propertyName is supplied it will
     * be used to store the timeout id.
     * @param {Object} timeoutSpec An object containing the details about that function, and the time delay.
     * @param {Function|String} timeoutSpec.fn The function to call, or name of function in this object to call. Used as the `name` parameter if a string.
     * @param {Number} timeoutSpec.delay The milliseconds to delay the call by.
     * @param {Object[]} timeoutSpec.args The arguments to pass.
     * @param {String} [timeoutSpec.name] The name under which to register the timer. Defaults to `fn.name`.
     * @param {Boolean} [timeoutSpec.runOnDestroy] Pass `true` if this function should be executed if the Delayable instance is destroyed while function is scheduled.
     * @param {Boolean} [timeoutSpec.cancelOutstanding] Pass `true` to cancel any outstanding invocation of the passed function.
     * @returns {Number}
     * @internal
     */
    setTimeout({ fn: fn2, delay: delay2, name, runOnDestroy, cancelOutstanding, args }) {
      if (arguments.length > 1 || typeof arguments[0] === "function") {
        [fn2, delay2, name, runOnDestroy, cancelOutstanding] = arguments;
      }
      if (typeof fn2 === "string") {
        name = fn2;
      } else if (!name) {
        name = fn2.name || fn2;
      }
      if (cancelOutstanding) {
        this.clearTimeout(name);
      }
      const me = this, timeoutIds = me.timeoutIds || (me.timeoutIds = /* @__PURE__ */ new Map()), timeoutMap = me.timeoutMap || (me.timeoutMap = /* @__PURE__ */ new Map()), timeoutId = setTimeout(() => {
        if (typeof fn2 === "string") {
          fn2 = me[name];
        }
        timeoutIds == null ? void 0 : timeoutIds.delete(timeoutId);
        timeoutMap == null ? void 0 : timeoutMap.delete(name);
        globalDelays == null ? void 0 : globalDelays.timeouts.delete(timeoutId);
        fn2.apply(me, args);
      }, delay2);
      timeoutIds.set(timeoutId, runOnDestroy ? fn2 : true);
      globalDelays == null ? void 0 : globalDelays.timeouts.set(timeoutId, {
        fn: fn2,
        delay: delay2,
        name
        /*, stack : new Error().stack*/
      });
      if (name) {
        timeoutMap.set(name, timeoutId);
      }
      return timeoutId;
    }
    /**
     * clearTimeout wrapper, either call with timeout id as normal clearTimeout or with timeout name (if you specified
     * a name to setTimeout())
     * property to null.
     * @param {Number|String} idOrName timeout id or name
     * @internal
     */
    clearTimeout(idOrName) {
      var _a6;
      let id2 = idOrName;
      if (typeof id2 === "string") {
        if (this.timeoutMap) {
          id2 = this.timeoutMap.get(idOrName);
          this.timeoutMap.delete(idOrName);
        } else {
          return;
        }
      }
      clearTimeout(id2);
      (_a6 = this.timeoutIds) == null ? void 0 : _a6.delete(id2);
      globalDelays == null ? void 0 : globalDelays.timeouts.delete(id2);
    }
    /**
     * clearInterval wrapper
     * @param {Number} id
     * @internal
     */
    clearInterval(id2) {
      var _a6;
      clearInterval(id2);
      (_a6 = this.intervalIds) == null ? void 0 : _a6.delete(id2);
      globalDelays == null ? void 0 : globalDelays.intervals.delete(id2);
    }
    /**
     * Same as native setInterval, but will be cleared automatically on destroy
     * @param {Function} fn callback method
     * @param {Number} delay delay in milliseconds
     * @param {String} name delay name for debugging
     * @returns {Number}
     * @internal
     */
    setInterval(fn2, delay2, name) {
      const intervalId = setInterval(fn2, delay2);
      (this.intervalIds || (this.intervalIds = /* @__PURE__ */ new Set())).add(intervalId);
      globalDelays == null ? void 0 : globalDelays.intervals.set(intervalId, { fn: fn2, delay: delay2, name });
      return intervalId;
    }
    /**
     * Relays to native requestAnimationFrame and adds to tracking to have call automatically canceled on destroy.
     * @param {Function} fn
     * @param {Object[]} [extraArgs] The argument list to append to those passed to the function.
     * @param {Object} [thisObj] `this` reference for the function.
     * @returns {Number}
     * @internal
     */
    requestAnimationFrame(fn2, extraArgs = [], thisObj = this) {
      const animationFrameIds = this.animationFrameIds || (this.animationFrameIds = /* @__PURE__ */ new Set()), frameId = requestAnimationFrame(() => {
        globalDelays == null ? void 0 : globalDelays.animationFrames.delete(frameId);
        animationFrameIds.delete(frameId) && fn2.apply(thisObj, extraArgs);
      });
      animationFrameIds.add(frameId);
      globalDelays == null ? void 0 : globalDelays.animationFrames.set(frameId, { fn: fn2, extraArgs, thisObj });
      return frameId;
    }
    /**
     * Relays to native requestIdleCallback and adds to tracking to have call automatically canceled on destroy.
     * @param {Function} fn
     * @param {Object[]} [extraArgs] The argument list to append to those passed to the function.
     * @param {Object} [thisObj] `this` reference for the function.
     * @returns {Number}
     * @internal
     */
    requestIdleCallback(fn2, extraArgs = [], thisObj = this) {
      const idleCallbackIds = this.idleCallbackIds || (this.idleCallbackIds = /* @__PURE__ */ new Set()), frameId = requestIdleCallback(() => {
        globalDelays == null ? void 0 : globalDelays.idleCallbacks.delete(frameId);
        idleCallbackIds.delete(frameId) && fn2.apply(thisObj, extraArgs);
      });
      idleCallbackIds.add(frameId);
      globalDelays == null ? void 0 : globalDelays.idleCallbacks.set(frameId, { fn: fn2, extraArgs, thisObj });
      return frameId;
    }
    /**
     * Creates a function which will execute once, on the next animation frame. However many time it is
     * called in one event run, it will only be scheduled to run once.
     * @param {Function|String} fn The function to call, or name of function in this object to call.
     * @param {Object[]} [args] The argument list to append to those passed to the function.
     * @param {Object} [thisObj] `this` reference for the function.
     * @param {Boolean} [cancelOutstanding] Cancel any outstanding queued invocation upon call.
     * @internal
     */
    createOnFrame(fn2, args = [], thisObj = this, cancelOutstanding) {
      let rafId;
      const result = (...callArgs) => {
        if (rafId != null && cancelOutstanding) {
          this.cancelAnimationFrame(rafId);
          rafId = null;
        }
        if (rafId == null) {
          rafId = this.requestAnimationFrame(() => {
            if (typeof fn2 === "string") {
              fn2 = thisObj[fn2];
            }
            rafId = null;
            callArgs.push(...args);
            fn2.apply(thisObj, callArgs);
          });
        }
      };
      result.cancel = () => this.cancelAnimationFrame(rafId);
      return result;
    }
    /**
     * Relays to native cancelAnimationFrame and removes from tracking.
     * @param {Number} handle
     * @internal
     */
    cancelAnimationFrame(handle) {
      var _a6;
      cancelAnimationFrame(handle);
      (_a6 = this.animationFrameIds) == null ? void 0 : _a6.delete(handle);
      globalDelays == null ? void 0 : globalDelays.animationFrames.delete(handle);
    }
    /**
     * Relays to native cancelIdleCallback and removes from tracking.
     * @param {Number} handle
     * @internal
     */
    cancelIdleCallback(handle) {
      var _a6;
      cancelIdleCallback(handle);
      (_a6 = this.idleCallbackIds) == null ? void 0 : _a6.delete(handle);
      globalDelays == null ? void 0 : globalDelays.idleCallbacks.delete(handle);
    }
    async nextAnimationFrame() {
      return new Promise((resolve) => this.requestAnimationFrame(resolve));
    }
    /**
     * Wraps a function with another function that delays it specified amount of time, repeated calls to the wrapper
     * resets delay.
     * @param {Function|String} fn The function to call. If this is a string, it is looked up as a method on `this`
     * instance (or `options.thisObj` instead, if provided).
     * @param {Object|Number} options The delay in milliseconds or an options object.
     * @param {Number} options.delay The delay in milliseconds.
     * @param {Array} [options.appendArgs] The argument list to append to those passed to the function.
     * @param {Object} [options.thisObj] The `this` reference for the function.
     * @returns {Function} Wrapped function to call.
     * @internal
     */
    buffer(fn2, options) {
      let delay2 = options;
      if (options && typeof options !== "number") {
        delay2 = options.delay;
      } else {
        options = null;
      }
      const bufferWrapFn = (...params) => {
        if (bufferWrapFn.suspended) {
          return;
        }
        const { delay: delay3 } = bufferWrapFn;
        bufferWrapFn.cancel();
        bufferWrapFn.called = false;
        bufferWrapFn.args = params;
        if (bufferWrapFn.immediate || !delay3) {
          invoker();
        } else {
          bufferWrapFn.timerId = this.setTimeout(invoker, delay3);
        }
      }, invoker = makeInvoker(this, fn2, bufferWrapFn, options);
      bufferWrapFn.delay = delay2;
      return decorateWrapFn(this, bufferWrapFn);
    }
    /**
     * Returns a function that when called will schedule a call to `fn` on the next animation frame.
     *
     * @param {Function|String} fn The function to call. If this is a string, it is looked up as a method on `this`
     * instance (or `options.thisObj` instead, if provided).
     * @param {Boolean|Object} [options] An options object or the `cancelOutstanding` boolean property of it.
     * @param {Boolean} [options.cancelOutstanding] Pass `true` to cancel any pending animation frame requests and
     * schedule a new one on each call to the returned function.
     * @param {Array} [options.appendArgs] The argument list to append to those passed to the function.
     * @param {Object} [options.thisObj] The `this` reference for the function.
     * @returns {Function}
     * @internal
     */
    raf(fn2, options) {
      let cancelOutstanding = options;
      if (options && typeof options !== "boolean") {
        cancelOutstanding = options.cancelOutstanding;
      } else {
        options = null;
      }
      const rafWrapFn = (...params) => {
        if (rafWrapFn.suspended) {
          return;
        }
        if (rafWrapFn.cancelOutstanding || rafWrapFn.immediate) {
          rafWrapFn.cancel();
        }
        rafWrapFn.called = false;
        rafWrapFn.args = params;
        if (rafWrapFn.immediate) {
          invoker();
        } else if (!rafWrapFn.isPending) {
          rafWrapFn.timerId = this.requestAnimationFrame(invoker);
        }
      }, invoker = makeInvoker(this, fn2, rafWrapFn, options);
      rafWrapFn.cancelOutstanding = cancelOutstanding;
      rafWrapFn.raf = true;
      return decorateWrapFn(this, rafWrapFn, "cancelAnimationFrame");
    }
    idle(fn2, options) {
      let cancelOutstanding = options;
      if (options && typeof options !== "boolean") {
        cancelOutstanding = options.cancelOutstanding;
      } else {
        options = null;
      }
      const idleWrapFn = (...params) => {
        if (idleWrapFn.suspended) {
          return;
        }
        if (idleWrapFn.cancelOutstanding || idleWrapFn.immediate) {
          idleWrapFn.cancel();
        }
        idleWrapFn.called = false;
        idleWrapFn.args = params;
        if (idleWrapFn.immediate) {
          invoker();
        } else if (!idleWrapFn.isPending) {
          idleWrapFn.timerId = this.requestIdleCallback(invoker);
        }
      }, invoker = makeInvoker(this, fn2, idleWrapFn, options);
      idleWrapFn.cancelOutstanding = cancelOutstanding;
      this.setTimeout(() => this.idleCallbackIds.delete(idleWrapFn.timerId) && idleWrapFn.now(), 100);
      return decorateWrapFn(this, idleWrapFn, "cancelIdleCallback");
    }
    /**
     * Create a "debounced" function which will call on the "leading edge" of a timer period.
     * When first invoked will call immediately, but invocations after that inside its buffer
     * period will be rejected, and *one* invocation will be made after the buffer period has expired.
     *
     * This is useful for responding immediately to a first mousemove, but from then on, only
     * calling the action function on a regular timer while the mouse continues to move.
     *
     * @param {Function|String} fn The function to call. If this is a string, it is looked up as a method on `this`
     * instance (or `options.thisObj` instead, if provided).
     * @param {Number|Object} options The milliseconds to wait after each execution before another execution takes place
     * or a object containing options.
     * @param {Object} [options.thisObj] `this` reference for the function.
     * @param {Array} [options.appendArgs] The argument list to append to those passed to the function.
     * @param {Function|String} [options.throttled] A function to call when the invocation is delayed due to buffer
     * time not having expired. If this is a string, it is looked up as a method on `this` instance (or `options.thisObj`
     * instead, if provided). When called, the same arguments are passed as would have been passed to `fn`, including
     * any `options.appendArgs`.
     * @internal
     */
    throttle(fn2, options) {
      let delay2 = options, throttled;
      if (options && typeof options !== "number") {
        delay2 = options.delay;
        throttled = options.throttled;
      } else {
        options = null;
      }
      const me = this, throttleWrapFn = (...args) => {
        if (throttleWrapFn.suspended) {
          return;
        }
        const { delay: delay3 } = throttleWrapFn, elapsed = performance2.now() - throttleWrapFn.lastCallTime;
        throttleWrapFn.args = args;
        if (throttleWrapFn.immediate || elapsed >= delay3) {
          me.clearTimeout(throttleWrapFn.timerId);
          invoker();
        } else {
          if (!throttleWrapFn.isPending) {
            throttleWrapFn.timerId = me.setTimeout(invoker, delay3 - elapsed);
            throttleWrapFn.called = false;
          }
          if (throttled) {
            throttled.wrapFn.args = args;
            throttled();
          }
        }
      }, invoker = makeInvoker(me, fn2, throttleWrapFn, options);
      throttleWrapFn.delay = delay2;
      if (throttled) {
        throttled = makeInvoker(me, throttled, () => {
        }, options);
      }
      return decorateWrapFn(me, throttleWrapFn);
    }
    static setupDelayable(cls) {
      cls.setupDelayableMethods(cls.delayable);
    }
    /**
     * This method initializes the `delayable` methods on this class.
     * @param {Object} delayable The `delayable` property.
     * @param {Function} [cls] This parameter will be used internally to process static methods.
     * @private
     */
    static setupDelayableMethods(delayable, cls = null) {
      const me = this, statics = delayable.static, target = cls || me.prototype;
      if (statics) {
        delete delayable.static;
      }
      for (const name in delayable) {
        let options = delayable[name];
        const implName = name + "Now", type = typeof options;
        if (!target[implName]) {
          target[implName] = target[name];
        }
        if (type === "number") {
          options = {
            type: "buffer",
            delay: options
          };
        } else if (type === "string") {
          options = {
            type: options
          };
        }
        defineProperty3(target, name, {
          get() {
            const me2 = this, caller = (...params) => me2[implName](...params), value2 = me2[options.type](caller, options);
            defineProperty3(me2, name, { value: value2, configurable: true, writable: true });
            caller.methodName = name;
            return value2;
          }
        });
      }
    }
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "Delayable"), _a5;
};

// ../Core/lib/Core/state/StateStorage.js
var StateStorage = class {
  /**
   * Returns an object with all stored keys and their values as its properties
   * @member {Object}
   */
  get data() {
    return /* @__PURE__ */ Object.create(null);
  }
  /**
   * Returns the stored keys as set by {@link #function-setItem}
   * @member {String[]}
   */
  get keys() {
    return [];
  }
  /**
   * Remove all stored keys
   */
  clear() {
  }
  /**
   * Returns key value as set by {@link #function-setItem}
   * @param {String} key
   * @returns {*}
   */
  getItem(key) {
    return null;
  }
  /**
   * Removes the specified key
   * @param {String} key
   */
  removeItem(key) {
  }
  /**
   * Sets the specified key to the given value
   * @param {String} key
   * @param {*} value The item value
   */
  setItem(key, value2) {
  }
};
StateStorage._$name = "StateStorage";

// ../Core/lib/Core/state/StateProvider.js
var Local = class extends StateStorage {
  constructor(stateProvider) {
    super();
    this.prefix = stateProvider.prefix || "";
  }
  get isLocal() {
    return true;
  }
  get data() {
    const data = empty(), keys = this.keys;
    for (const key of keys) {
      data[key] = this.getItem(key);
    }
    return data;
  }
  get keys() {
    return getKeys(this.prefix, this.prefix.length);
  }
  clear() {
    const keys = getKeys(this.prefix);
    for (const key of keys) {
      localStorage.removeItem(key);
    }
  }
  getItem(key) {
    const value2 = localStorage.getItem(this.prefix + key);
    return value2 === null ? value2 : JSON.parse(value2);
  }
  removeItem(key) {
    return localStorage.removeItem(this.prefix + key);
  }
  setItem(key, value2) {
    return localStorage.setItem(this.prefix + key, JSON.stringify(value2));
  }
};
var Memory = class extends StateStorage {
  constructor() {
    super();
    this.clear();
  }
  get isMemory() {
    return true;
  }
  get data() {
    return ObjectHelper.clone(this._data);
  }
  get keys() {
    return Object.keys(this._data);
  }
  clear() {
    this._data = empty();
  }
  getItem(key) {
    return key in this._data ? this._data[key] : null;
  }
  removeItem(key) {
    delete this._data[key];
  }
  setItem(key, value2) {
    this._data[key] = value2;
  }
};
var empty = () => /* @__PURE__ */ Object.create(null);
var getKeys = (prefix, pos = 0) => {
  const keys = [], count = localStorage.length;
  for (let key, i = 0; i < count; ++i) {
    key = localStorage.key(i);
    key.startsWith(prefix) && keys.push(key.slice(pos));
  }
  return keys;
};
var nullStorage = new StateStorage();
var storageTypes = {
  local: Local,
  memory: Memory
};
var _StateProvider = class _StateProvider extends Base.mixin(Delayable_default, Events_default) {
  static get configurable() {
    return {
      /**
       * The key prefix applied when using the `'local'` {@link #config-storage} type.
       * @config {String}
       * @default
       */
      prefix: "bryntum-state:",
      /**
       * Storage instance
       * @member {Core.state.StateStorage} storage
       */
      /**
       * One of the following storage types:
       *  - `local` : Stores data in the browser's `localStorage` using the {@link #config-prefix}.
       *  - `memory` : Stores data in the provider's memory.
       *
       * @config {'local'|'memory'|Core.state.StateStorage}
       * @default
       */
      storage: "local"
    };
  }
  static get delayable() {
    return {
      writeStatefuls: 50
    };
  }
  /**
   * The default {@link Core.mixin.State#config-stateProvider} for stateful objects.
   * @property {Core.state.StateProvider}
   */
  static get instance() {
    return this._instance;
  }
  static set instance(inst) {
    if (inst == null) {
      inst = nullProvider;
    } else {
      if (typeof inst === "string" || ObjectHelper.isClass(inst) || inst instanceof StateStorage) {
        inst = {
          storage: inst
        };
      }
      if (ObjectHelper.isObject(inst)) {
        inst = new _StateProvider(inst);
      }
    }
    this._instance = inst;
  }
  /**
   * Initializes the default `StateProvider` instance for the page. This method can be passed an instance or one of
   * the following type aliases:
   *
   *  - `'local'` : use `localStorage` to store application state (most common)
   *  - `'memory'` : holds application state in the `StateProvider` instance (used when state is saved to a server)
   *
   * Once the `StateProvider` is initialized, components that use {@link Core.mixin.State} and assign components a
   * {@link Core.mixin.State#config-stateId} will use this default provider to automatically save and restore their
   * state.
   *
   * @param {'local'|'memory'|Core.state.StateProvider} inst The state provider storage type ('local' or 'memory') or
   * the `StateProvider` instance.
   * @returns {Core.state.StateProvider}
   */
  static setup(inst) {
    this.instance = inst;
    return this.instance;
  }
  doDestroy() {
    self.writeStatefuls.flush();
    super.doDestroy();
  }
  /**
   * On read, this property returns all state data stored in the provider. On write, this property _adds_ all the
   * given values to the state provider's data. To replace the data, call {@link #function-clear} before assigning
   * this property. This is used to bulk populate this `StateProvider` with data for stateful components.
   * @member {Object}
   */
  get data() {
    return this.storage.data;
  }
  set data(data) {
    if (!data) {
      this.clear();
    } else {
      for (const key in data) {
        this.setValue(key, data[key]);
      }
    }
  }
  /**
   * Clears all state date
   * @returns {Core.state.StateProvider} this instance
   */
  clear() {
    this.storage.clear();
    return this;
  }
  changeStorage(storage) {
    if (storage == null) {
      storage = nullStorage;
    } else {
      if (typeof storage === "string") {
        if (!storageTypes[storage]) {
          throw new Error(`Invalid storage type "${storage}" (expected one of: "${Object.keys(storageTypes).join('", "')}")`);
        }
        storage = storageTypes[storage];
      }
      if (ObjectHelper.isClass(storage)) {
        storage = new storage(this);
      }
    }
    return storage;
  }
  /**
   * This method is called to schedule saving the given `stateful` object.
   * @param {Core.mixin.State} stateful The stateful object to save.
   * @param {Object} [options] An object of options that affect the state saving process.
   * @param {String} [options.id] The key for the saved state.
   * @param {Boolean} [options.immediate] Pass `true` to save the data synchronously instead of on a delay.
   * @internal
   */
  saveStateful(stateful, options) {
    (this.pendingSaves || (this.pendingSaves = [])).push([stateful, options]);
    this.writeStatefuls();
  }
  /**
   * A delayable method that flushes pending stateful objects.
   * @private
   */
  writeStatefuls() {
    const me = this, { pendingSaves } = me, n = pendingSaves == null ? void 0 : pendingSaves.length, stateIds = [], saved = [];
    me.pendingSaves = null;
    if (n) {
      for (let options, stateful, stateId, i = 0; i < n; ++i) {
        [stateful, options] = pendingSaves[i];
        if (!stateful.isDestroying && stateful.isSaveStatePending) {
          stateId = stateful.saveState({
            ...options,
            immediate: true
          });
          if (stateId) {
            stateIds.push(stateId);
            saved.push(stateful);
          }
        }
      }
      if (stateIds.length) {
        me.trigger("save", {
          stateIds,
          saved
        });
      }
    }
  }
  /**
   * Returns the stored state given its `key`.
   * @param {String} key The identifier of the state to return.
   * @returns {Object}
   */
  getValue(key) {
    this.writeStatefuls.flush();
    return this.storage.getItem(key);
  }
  /**
   * Stores the given state `value` under the specified `key`.
   * @param {String} key The identifier of the state value.
   * @param {Object} value The state value to set.
   * @returns {Core.state.StateProvider} this instance
   */
  setValue(key, value2) {
    const me = this, { storage } = me, was = me.getValue(key);
    if (value2 != null) {
      storage.setItem(key, value2);
      me.trigger("set", { key, value: value2, was });
    } else if (was !== null) {
      storage.removeItem(key);
      me.trigger("remove", { key, was });
    }
    return me;
  }
};
__publicField(_StateProvider, "$name", "StateProvider");
var StateProvider = _StateProvider;
var nullProvider = new StateProvider({
  storage: nullStorage
});
StateProvider._instance = nullProvider;
StateProvider._$name = "StateProvider";

// ../Core/lib/Core/mixin/State.js
var primitiveRe = /boolean|number|string/;
var State_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base).mixin(Events_default) {
    afterConstruct() {
      super.afterConstruct();
      this.stateProvider && this.loadState();
    }
    finalizeInit() {
      this.stateProvider && this.loadState();
      super.finalizeInit();
    }
    /**
     * Returns `true` if this instance implements the {@link Core.mixin.State} interface.
     * @property {Boolean}
     * @readonly
     * @advanced
     */
    get isStateful() {
      return true;
    }
    /**
     * Returns `true` if this instance is ready to participate in state activities.
     * @property {Boolean}
     * @readonly
     * @internal
     */
    get isStatefulActive() {
      return !this.statefulSuspended && !this.isResponsivePending && !this.isResponsiveUpdating;
    }
    // state
    /**
     * Gets or sets a component's state
     * @property {Object}
     * @typings {*}
     * @category State
     */
    get state() {
      return this._state = this.getState();
    }
    set state(state) {
      this._state = state;
      if (state) {
        if (this.trigger("beforeStateApply", { state }) !== false) {
          this.applyState(state);
        }
        ;
      }
    }
    // statefulEvents
    updateStatefulEvents(events) {
      var _a6;
      const me = this, listeners = {
        name: "statefulEvents",
        thisObj: me
      };
      me.detachListeners(listeners.name);
      if (events) {
        if (typeof events === "string") {
          events = StringHelper.split(events);
        } else if (!Array.isArray(events)) {
          events = ObjectHelper.getTruthyKeys(events);
        }
        if (events.length) {
          for (const event of events) {
            listeners[event] = "onStatefulEvent";
          }
          (_a6 = me.ion) == null ? void 0 : _a6.call(me, listeners);
        }
      }
    }
    // statefulId
    /**
     * Returns the state key to use for this instance. This will be either the {@link #config-stateId} or the
     * {@link Core.widget.Widget#config-id} (if explicitly specified and {@link #config-stateful} is not `false`).
     * @property {String}
     * @category State
     * @internal
     */
    get statefulId() {
      const me = this, { responsiveState } = me;
      let statefulId = me.stateId;
      if (statefulId == null && me.hasGeneratedId === false && me.stateful !== false) {
        statefulId = me.id;
      }
      if (statefulId && responsiveState) {
        statefulId = `${statefulId}[${responsiveState}]`;
      }
      return statefulId;
    }
    // statefulness
    /**
     * Returns an object whose truthy keys are the config properties to include in this object's {@link #property-state}.
     * @property {Object}
     * @category State
     * @readonly
     * @private
     */
    get statefulness() {
      const { stateful } = this;
      return Array.isArray(stateful) ? ObjectHelper.createTruthyKeys(stateful) : stateful;
    }
    // stateProvider
    get stateProvider() {
      var _a6;
      return (_a6 = this._stateProvider) != null ? _a6 : this._stateId && StateProvider.instance;
    }
    //---------------------------------------------------------------------------------------------------------------
    // Methods
    /**
     * Applies the given `state` to this instance.
     *
     * This method is not called directly, but is called when the {@link #property-state} property is assigned a value.
     *
     * This method is implemented by derived classes that have complex state which exceeds the simple list of config
     * properties provided by {@link #config-stateful}. In these cases, the `super` method can be called to handle any
     * config properties that are part of the complex state. The default implementation of this method will only assign
     * those config properties listed in {@link #config-stateful} from the provided `state` object.
     *
     * @param {Object} state The state object to apply to this instance.
     * @category State
     * @advanced
     */
    applyState(state) {
      state = this.pruneState(state);
      if (state) {
        this.setConfig(state);
      }
    }
    /**
     * Returns this object's state information.
     *
     * This method is not called directly, but is called to return the value of the {@link #property-state} property.
     *
     * This method is implemented by derived classes that have complex state which exceeds the simple list of config
     * properties provided by {@link #config-stateful}. In these cases, the `super` method can be called to gather the
     * config properties that are part of the complex state. The default implementation of this method will only copy
     * those config properties listed in {@link #config-stateful} to the returned `state` object.
     *
     * @returns {Object}
     * @category State
     * @advanced
     */
    getState() {
      const me = this, {
        initialConfig,
        statefulness,
        isConstructing: defaultState
      } = me, { configs } = me.$meta, source = defaultState ? Object.assign(Object.create(me.$meta.config), initialConfig) : me;
      let state = null, key, value2;
      if (statefulness) {
        state = {};
        for (key in statefulness) {
          if (statefulness[key]) {
            value2 = source[key];
            if (value2 == null ? void 0 : value2.isStateful) {
              value2 = value2.state;
            } else if (!defaultState) {
              if (ObjectHelper.isDate(value2)) {
                value2 = DateHelper.format(value2, "YYYY-MM-DDTHH:mm:ssZ");
              }
              if (configs[key].equal(value2, initialConfig == null ? void 0 : initialConfig[key]) || !primitiveRe.test(typeof value2)) {
                continue;
              }
            }
            state[key] = value2;
          }
        }
      }
      return state;
    }
    /**
     * Loads this object's state from its {@link #config-stateProvider} and applies it to its {@link #property-state}.
     *
     * This method only acts upon its first invocation for a given instance (unless `true` is passed for the `reload`
     * parameter). This allows for flexibility in the timing of that call during the early stages of the instances'
     * lifecycle. To reload the state after this time, manually assign the desired value to the {@link #property-state}
     * property or call this method and pass `reload` as `true`.
     *
     * This method is called automatically during construction when a {@link #config-stateId} or (in some cases) an
     * explicit {@link Core.widget.Widget#config-id} is provided.
     *
     * @param {String} [stateId] An overriding key to use instead of this object's {@link #config-stateId}.
     * @param {Boolean} [reload=false] Pass `true` to load the state even if previously loaded.
     * @category State
     */
    loadState(stateId, reload) {
      if (typeof stateId === "boolean") {
        reload = stateId;
        stateId = null;
      }
      const me = this, { statefulLoaded } = me;
      if (me.isStatefulActive && (reload || !statefulLoaded)) {
        const state = me.loadStatefulData(stateId || (stateId = me.statefulId));
        if (!statefulLoaded && stateId) {
          me.defaultState = me.state;
          me.statefulLoaded = true;
        }
        if (state) {
          me.state = state;
        }
      }
    }
    loadStatefulData(stateId) {
      var _a6;
      stateId = this.isStatefulActive ? stateId || this.statefulId : null;
      return stateId && ((_a6 = this.stateProvider) == null ? void 0 : _a6.getValue(stateId));
    }
    resetDefaultState() {
      if (this.defaultState) {
        this.state = this.defaultState;
      }
    }
    resumeStateful(full = false) {
      this.statefulSuspended = full ? 0 : Math.max(this.statefulSuspended - 1, 0);
    }
    /**
     * Saves this object's state to its {@link #config-stateProvider}.
     *
     * When a {@link #config-stateId} or (in some cases) an explicit {@link Core.widget.Widget#config-id} is provided,
     * this method will be called automatically any time a config property listed in {@link #config-stateful} changes or
     * when a {@link #config-statefulEvents stateful event} is fired.
     *
     * Derived classes are responsible for calling this method whenever the persistent {@link #property-state} of the
     * object changes.
     *
     * @param {Object|String} [options] Options that affect the state saving process or, if a string, the state `id`.
     * @param {String} [options.id] The state id for the saved state (overrides {@link #config-stateId}).
     * @param {Boolean} [options.immediate] Pass `true` to save the data synchronously instead of on a delay.
     * @category State
     */
    saveState(options) {
      if (typeof options === "string") {
        options = {
          id: options
        };
      } else {
        options = options || {};
      }
      const me = this, { stateProvider } = me, statefulId = options.id || me.isStatefulActive && me.statefulId;
      if (statefulId && stateProvider) {
        if (options.immediate) {
          me.isSaveStatePending = false;
          const { state } = me;
          if (me.trigger("beforeStateSave", { state }) !== false) {
            stateProvider.setValue(statefulId, state);
          }
        } else if (!me.isSaveStatePending) {
          me.isSaveStatePending = true;
          stateProvider.saveStateful(me, options);
        }
        return statefulId;
      }
    }
    suspendStateful() {
      ++this.statefulSuspended;
    }
    //---------------------------------------------------------------------------------------------------------------
    // Private / Internal
    onConfigChange({ name, value: value2, was, config }) {
      super.onConfigChange({ name, value: value2, was, config });
      if (!this.isConstructing && this.isStatefulActive && this.statefulId) {
        const { stateful } = this;
        if (Array.isArray(stateful) ? stateful.includes(name) : stateful == null ? void 0 : stateful[name]) {
          this.saveState();
        }
      }
    }
    onStatefulEvent() {
      if (!this.isConstructing) {
        this.saveState();
      }
    }
    /**
     * Returns an object that copies the {@link #config-stateful} config properties from the provided `state` object.
     *
     * @param {Object} state A state object from which to copy stateful configs.
     * @returns {Object}
     * @category State
     * @private
     */
    pruneState(state) {
      const { statefulness } = this;
      if (statefulness) {
        const pruned = {};
        for (const key in state) {
          if (statefulness[key]) {
            pruned[key] = state[key];
          }
        }
        state = pruned;
      }
      return state;
    }
    //---------------------------------------------------------------------------------------------------------------
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "State"), // region Events
  /**
   * Fired before state is saved by the StateProvider. Allows editing the state object or preventing the operation.
   * @event beforeStateSave
   * @preventable
   * @param {Object} event The Bryntum event object
   * @param {Object} event.state State object config
   */
  /**
   * Fired before state is applied to the source. Allows editing the state object or preventing the operation.
   * @event beforeStateApply
   * @preventable
   * @param {Object} event The Bryntum event object
   * @param {Object} event.state State object config
   */
  // endregion
  __publicField(_a5, "configurable", {
    /**
     * This value can be one of the following:
     *
     * - `false` to not use an explicitly assigned {@link Core.widget.Widget#config-id} as the component's
     * {@link #config-stateId} (this is only necessary when there is a {@link #config-stateProvider}).
     * - An array of strings naming the config properties to save in the component's {@link #property-state}
     * object.
     * - An object whose truthy keys are the config properties to save in the component's {@link #property-state}
     * object.
     *
     * These last two uses of the `stateful` config property do not apply to components that have a complex
     * state, as described in the {@link Core.mixin.State State mixin documentation}.
     *
     * This config property is typically set by derived classes to a value including any config property that
     * the user can affect via the user interface. For example, the {@link Core.widget.Panel#config-collapsed}
     * config property is listed for a {@link Core.widget.Panel} since the user can toggle this config property
     * using the {@link Core.widget.panel.PanelCollapser#config-tool collapse tool}.
     *
     * @config {Boolean|Object|String[]}
     * @category State
     */
    stateful: {
      value: null,
      $config: {
        merge: "classList"
      }
    },
    /**
     * The events that, when fired by this component, should trigger it to save its state by calling
     * {@link #function-saveState}.
     *
     * ```javascript
     *  class MyStatefulComponent extends Base.mixin(State) {
     *      static get configurable() {
     *          return {
     *              statefulEvents : [ 'change', 'resize' ]
     *          };
     *      }
     *  }
     * ```
     * In the above example, {@link #function-saveState} will be called any time an instance of this class
     * fires the `change` or `resize` event.
     *
     * This config is typically set by derived classes as a way to ensure {@link #function-saveState} is called
     * whenever their persistent state changes.
     *
     * @config {Object|String[]}
     * @category State
     * @default
     */
    statefulEvents: {
      $config: {
        merge: "classList"
      },
      value: ["stateChange"]
    },
    /**
     * The key to use when saving this object's state in the {@link #config-stateProvider}. If this config is
     * not assigned, and {@link #config-stateful} is not set to `false`, the {@link Core.widget.Widget#config-id}
     * (if explicitly specified) will be used as the `stateId`.
     *
     * If neither of these is given, the {@link #function-loadState} and {@link #function-saveState} methods
     * will need to be called directly to make use of the `stateProvider`.
     *
     * For single page applications (SPA's), or multi-page applications (MPA's) that have common, stateful
     * components on multiple pages, the `stateId` should be unique across all stateful components (similar to DOM
     * element id's). MPA's that want each page to be isolated can more easily achieve that isolation using the
     * {@link Core.state.StateProvider#config-prefix}.
     *
     * @config {String}
     * @category State
     */
    stateId: null,
    /**
     * The `StateProvider` to use to save and restore this object's {@link #property-state}. By default, `state`
     * will be saved using the {@link Core.state.StateProvider#property-instance-static default state provider}.
     *
     * This config is useful for multi-page applications that have a set of common components that want to share
     * state across pages, as well as other components that want their state to be isolated. One of these groups
     * of stateful components could be assigned an explicit `stateProvider` while the other group could use the
     * default state provider.
     *
     * @config {Core.state.StateProvider}
     * @category State
     */
    stateProvider: null
  }), __publicField(_a5, "prototypeProperties", {
    statefulLoaded: false,
    statefulSuspended: 0
  }), _a5;
};

// ../Core/lib/Core/mixin/Identifiable.js
var idCounts = ObjectHelper.getPathDefault(globalThis, "bryntum.idCounts", /* @__PURE__ */ Object.create(null));
var idTypes = {
  string: 1,
  number: 1
};
var Identifiable_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    static get declarable() {
      return [
        "identifiable"
      ];
    }
    static get configurable() {
      return {
        /**
         * The id of this object.  If not specified one will be generated. Also used for lookups through the
         * static `getById` of the class which mixes this in. An example being {@link Core.widget.Widget}.
         *
         * For a {@link Core.widget.Widget Widget}, this is assigned as the `id` of the DOM
         * {@link Core.widget.Widget#config-element element} and must be unique across all elements
         * in the page's `document`.
         * @config {String}
         * @category Common
         */
        id: ""
      };
    }
    static setupIdentifiable(cls, meta) {
      const { identifiable } = cls;
      identifiable.idMap = /* @__PURE__ */ Object.create(null);
      Reflect.defineProperty(cls, "identifiable", {
        get() {
          return identifiable;
        }
      });
    }
    doDestroy() {
      this.constructor.unregisterInstance(this);
      super.doDestroy();
    }
    changeId(id2) {
      return (this.hasGeneratedId = !id2) ? this.generateAutoId() : id2;
    }
    updateId(id2, oldId) {
      const me = this, C = me.constructor;
      oldId && C.unregisterInstance(me, oldId);
      if (!me.hasGeneratedId || C.identifiable.registerGeneratedId !== false) {
        C.registerInstance(me, id2);
      }
    }
    /**
     * This method generates an id for this instance.
     * @returns {String}
     * @internal
     */
    generateAutoId() {
      return this.constructor.generateId(`b-${this.$$name.toLowerCase()}-`);
    }
    static get all() {
      return Object.values(this.identifiable.idMap);
    }
    /**
     * Generate a new id, using an internal counter and a prefix.
     * @param {String} prefix Id prefix
     * @returns {String} Generated id
     */
    static generateId(prefix = "generatedId") {
      return prefix + (idCounts[prefix] = (idCounts[prefix] || 0) + 1);
    }
    static registerInstance(instance, instanceId = instance.id) {
      const { idMap } = this.identifiable;
      if (instanceId in idMap && !this.disableThrow) {
        throw new Error("Id " + instanceId + " already in use");
      }
      idMap[instanceId] = instance;
    }
    /**
     * Unregister Identifiable instance, normally done on destruction
     * @param {Object} instance Object to unregister
     * @param {String} id The id of the instance to unregister.
     */
    static unregisterInstance(instance, id2 = instance.id) {
      const { idMap } = this.identifiable;
      if (idTypes[typeof instance]) {
        delete idMap[instance];
      } else if (idMap[id2] === instance) {
        delete idMap[id2];
      }
    }
    static getById(id2) {
      const idMap = this.identifiable.idMap;
      if (idMap) {
        return idMap[id2];
      }
    }
    static get registeredInstances() {
      const idMap = this.identifiable.idMap;
      return idMap ? Object.values(idMap) : [];
    }
  }, __publicField(_a5, "$name", "Identifiable"), _a5;
};

// ../Core/lib/Core/data/stm/mixin/ModelStm.js
var STM_PROP = Symbol("STM_PROP");
var unrecordedFields = {
  // This field's value is a by product of node insertion and must not be recorded here.
  // It's the node insertion operation which is recorded by STM.
  parentIndex: 1,
  // Ordered parent index is similar to parentIndex - it is recalculated on node insertion. No need to track it
  // https://github.com/bryntum/support/issues/8257
  orderedParentIndex: 1
};
var ModelStm_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    static get defaultConfig() {
      return {
        stm: null
      };
    }
    joinStore(store) {
      if (!this.stm) {
        this.stm = store.stm;
      }
    }
    unjoinStore(store, isReplacing = false) {
      var _a6;
      if (this.stm === store.stm) {
        this.stm = null;
      }
      (_a6 = super.unjoinStore) == null ? void 0 : _a6.call(this, store, isReplacing);
    }
    /**
     * Reference to STM manager, if used
     * @member {Core.data.stm.StateTrackingManager}
     * @category Misc
     */
    get stm() {
      return this[STM_PROP];
    }
    set stm(stm) {
      this[STM_PROP] = stm;
    }
    // Hook for chronograph entity field accessors, for example; task.duration = 123.
    // Triggers before setting the value.
    beforeChronoFieldSet(fieldName, value2) {
      var _a6;
      const me = this;
      if (!me.inSetting && ((_a6 = me.stm) == null ? void 0 : _a6.enabled) && !unrecordedFields[fieldName] && !me.constructor.nonPersistableFields[fieldName]) {
        if (me.getFieldDefinition(fieldName)) {
          return { [fieldName]: { value: value2, oldValue: me.graph.activeTransaction.readCurrentOrProposedOrPrevious(me.$[fieldName]) } };
        }
      }
      return null;
    }
    // Hook for chronograph entity field accessors, for example; task.duration = 123
    // Triggers after setting the value.
    afterChronoFieldSet(fieldName, value2, wasSet) {
      wasSet && this.afterSet(fieldName, value2, false, false, wasSet, true);
    }
    shouldRecordFieldChange(fieldName, oldValue, newValue) {
      const store = this.firstStore;
      return !(this.constructor.nonPersistableFields[fieldName] || oldValue == null && newValue == null || oldValue === newValue || oldValue instanceof Date && newValue instanceof Date && oldValue.getTime() === newValue.getTime() || fieldName === "id" || fieldName === "$PhantomId" || fieldName === "parentId" && store && store.oldIdMap[oldValue] === store.getById(newValue));
    }
    /**
     * Overridden to store initial data of the changed fields and to notify STM
     * manager about the change action if anything has been changed in result.
     *
     * The method is called from within {@link Core/data/Model#function-set} method.
     *
     * @private
     */
    afterSet(field, value2, silent, fromRelationUpdate, wasSet, isChronoFieldSet) {
      const { stm } = this, nonPersistableFields = this.constructor.nonPersistableFields;
      if ((stm == null ? void 0 : stm.isBase) && stm.shouldRecordAction && !unrecordedFields[field] && !nonPersistableFields[field]) {
        if (wasSet) {
          let shouldRecord;
          const [newData, oldData] = Object.keys(wasSet).reduce(
            (data, fieldName) => {
              const { value: value3, oldValue } = wasSet[fieldName];
              if (this.shouldRecordFieldChange(fieldName, oldValue, value3)) {
                shouldRecord = true;
                data[0][fieldName] = value3;
                data[1][fieldName] = oldValue;
              }
              return data;
            },
            [{}, {}]
          );
          if (shouldRecord) {
            stm.onModelUpdate(this, newData, oldData, isChronoFieldSet);
          }
        }
      }
    }
    /**
     * Called from {@link Core/data/mixin/TreeNode#function-insertChild} to obtain inserted
     * records initial parents and parent index, to be able to restore the state back upon undo.
     *
     * @param {Core.data.Model[]} childRecords
     * @returns {Array} Array of results from this call and any of super calls if any.
     *               This result is consumed by {@link #function-afterInsertChild} which pops
     *               from the result array to take only results of this method call and leave
     *               results from super calls untouched.
     *
     * @private
     */
    beforeInsertChild(childRecords) {
      var _a6;
      const preResult = ((_a6 = super.beforeInsertChild) == null ? void 0 : _a6.call(this, childRecords)) || [], { stm } = this;
      if (stm == null ? void 0 : stm.enabled) {
        preResult.push(
          childRecords.reduce((result, childRecord) => {
            if (childRecord.root === this.root) {
              result.set(childRecord, {
                parent: childRecord.parent,
                index: childRecord.parent ? childRecord.parentIndex : void 0
              });
            }
            return result;
          }, /* @__PURE__ */ new Map())
        );
      }
      return preResult;
    }
    /**
     * Called from {@link Core/data/mixin/TreeNode#function-insertChild} to notify {@link Core/data/stm/StateTrackingManager}
     * about children insertion. Provides it with all necessary context information collected
     * in {@link #function-beforeInsertChild} required to undo/redo the action.
     *
     * @private
     */
    afterInsertChild(index, childRecords, beforeResult, inserted, orderedBeforeNode) {
      var _a6;
      const { stm } = this;
      if (stm == null ? void 0 : stm.shouldRecordAction) {
        const context = beforeResult.pop();
        if (inserted) {
          stm.onModelInsertChild(this, index, inserted, context, orderedBeforeNode);
        }
      }
      (_a6 = super.afterInsertChild) == null ? void 0 : _a6.call(this, index, childRecords, beforeResult, inserted);
    }
    /**
     * Called from {@link Core/data/mixin/TreeNode#function-removeChild} to obtain removed
     * records initial parent index, to be able to restore the state back upon undo.
     *
     * @param {Core.data.Model[]} childRecords
     * @param {Boolean} isMove
     * @returns {Array} Array of results from this call and any of super calls if any.
     *               This result is consumed by {@link #function-afterRemoveChild} which pops
     *               from the result array to take only results of this method call and leave
     *               results from super calls untouched.
     *
     * @private
     */
    beforeRemoveChild(childRecords, isMove) {
      const preResult = super.beforeRemoveChild ? super.beforeRemoveChild(childRecords, isMove) : [], { stm } = this;
      if ((stm == null ? void 0 : stm.shouldRecordAction) && !isMove) {
        preResult.push(
          childRecords.reduce((result, childRecord) => {
            result.set(childRecord, { parentIndex: childRecord.parentIndex, orderedParentIndex: childRecord.orderedParentIndex });
            return result;
          }, /* @__PURE__ */ new Map())
        );
      }
      return preResult;
    }
    /**
     * Called from {@link Core/data/mixin/TreeNode#function-removeChild} to notify {@link Core/data/stm/StateTrackingManager}
     * about children removing. Provides it with all necessary context information collected
     * in {@link #function-beforeRemoveChild} required to undo/redo the action.
     *
     * @private
     */
    afterRemoveChild(childRecords, beforeResult, isMove) {
      var _a6;
      const { stm } = this;
      if ((stm == null ? void 0 : stm.shouldRecordAction) && !isMove) {
        const context = beforeResult.pop();
        if (childRecords && childRecords.length) {
          stm.onModelRemoveChild(this, childRecords, context);
        }
      }
      (_a6 = super.afterRemoveChild) == null ? void 0 : _a6.call(this, childRecords, beforeResult, isMove);
    }
  }, __publicField(_a5, "$name", "ModelStm"), _a5;
};

// ../Core/lib/Core/data/mixin/TreeNode.js
var defaultTraverseOptions = {
  includeFilteredOutRecords: false
};
var fixTraverseOptions = (options) => {
  options = options || false;
  if (typeof options === "boolean") {
    options = {
      includeFilteredOutRecords: options
    };
  }
  return options || defaultTraverseOptions;
};
var TreeNode_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    /**
     * This static configuration option allows you to control whether an empty parent task should be converted into a
     * leaf. Enable/disable it for a whole class:
     *
     * ```javascript
     * Model.convertEmptyParentToLeaf = false;
     * ```
     *
     * By specifying `true`, all empty parents will be considered leafs. Can also be assigned a configuration object
     * with the following Boolean properties to customize the behaviour:
     *
     * ```javascript
     * Model.convertEmptyParentToLeaf = {
     *     onLoad   : false,
     *     onRemove : true
     * }
     * ```
     *
     * @member {Boolean|{ onLoad : Boolean, onRemove : Boolean }} convertEmptyParentToLeaf
     * @property {Boolean} onLoad Apply the transformation on load to any parents without children (`children : []`)
     * @property {Boolean} onRemove Apply the transformation when all children have been removed from a parent
     * @default false
     * @static
     * @category Parent & children
     * */
    static set convertEmptyParentToLeaf(value2) {
      if (value2 === true) {
        value2 = {
          onLoad: true,
          onRemove: true
        };
      } else if (value2 === false) {
        value2 = {
          onLoad: false,
          onRemove: false
        };
      }
      this._convertEmptyParentToLeaf = value2;
    }
    constructor(...args) {
      super(...args);
      if (this.children) {
        this.orderedChildren = this.orderedChildren || [];
      }
    }
    static get convertEmptyParentToLeaf() {
      return this._convertEmptyParentToLeaf || { onLoad: false, onRemove: false };
    }
    /**
     * This is a read-only property providing access to the parent node.
     * @member {Core.data.Model} parent
     * @readonly
     * @category Parent & children
     */
    /**
     * This is a read-only field provided in server synchronization packets to specify
     * which record id is the parent of the record.
     * @readonly
     * @field {String|Number|null} parentId
     * @category Tree
     */
    /**
     * This is a read-only field provided in server synchronization packets to specify
     * which position the node takes in the parent's children array.
     * This index is set on load and gets updated automatically after row reordering, sorting, etc.
     * To save the order, need to persist the field on the server and when data is fetched to be loaded,
     * need to sort by this field.
     * @readonly
     * @field {Number} parentIndex
     * @category Tree
     */
    /**
     * This is a read-only field provided in server synchronization packets to specify
     * which position the node takes in the parent's ordered children array.
     * This index is set on load and gets updated on reordering nodes in tree. Sorting and filtering
     * have no effect on it.
     * @readonly
     * @field {Number} orderedParentIndex
     * @category Tree
     */
    /**
     * This field is added to the class at runtime when the Store is configured with
     * {@link Core/data/Store#config-lazyLoad}. The
     * number specified should reflect the **total** amount of children of a parent node, including nested descendants.
     * @field {Number} remoteChildCount
     * @category Tree
     * @deprecated This field has been deprecated. Please read the [guide](#Grid/guides/data/lazyloading.md) to find out if
     * your app needs to use the new {@link #field-isFullyLoaded} field.
     */
    /**
     * This field is added to the class at runtime when the Store is configured with
     * {@link Core/data/Store#config-lazyLoad}. If set on a parent record at load time, that parent will not cause any
     * more child load requests. If omitted, it will be automatically set to `true` when a load request receives fewer
     * child records than requested.
     *
     * To specifically set this field for a parent at its children load request, provide it as a property in the data
     * response object:
     *
     * ```javascript
     * return {
     *    data : [ ... ],
     *    isFullyLoaded : true
     *    total : 123
     * }
     * ```
     *
     * Or to include grandchildren for a parent, it can be provided as a property on the record data object:
     *
     * ```javascript
     * return {
     *    data : [{
     *       id : 1,
     *       name : 'My Parent',
     *       isFullyLoaded : true,
     *       expanded : true,
     *       children : [{
     *          id : 2,
     *          name : 'My Child'
     *      }]
     *    }],
     *    total : 123
     * }
     * ```
     *
     * @field {Boolean} isFullyLoaded
     * @category Tree
     */
    ingestChildren(childRecord, stores2 = this.stores) {
      var _a6, _b2, _c2;
      const { inProcessChildren, constructor: MyClass } = this, store = stores2 == null ? void 0 : stores2[0];
      if (childRecord === true) {
        if (inProcessChildren) {
          return true;
        }
        return [];
      }
      if (childRecord) {
        let childRecords = ArrayHelper.asArray(childRecord);
        if (!childRecords.length) {
          return [];
        }
        const len = childRecords.length, result = [];
        childRecords = (_a6 = store == null ? void 0 : store.cloneImmutable(childRecords)) != null ? _a6 : childRecords;
        for (let i = 0, child; i < len; i++) {
          child = childRecords[i];
          child = child.isModel ? child : (_b2 = store == null ? void 0 : store.createRecord(child, false, true)) != null ? _b2 : new MyClass(child, null, null, true);
          child = (_c2 = store == null ? void 0 : store.processRecord(child)) != null ? _c2 : child;
          result.push(child);
        }
        if (this.children === true && store) {
          const sorter = store.createSorterFn(store.sorters);
          result.sort(sorter);
        }
        return result;
      }
    }
    /**
     * Child nodes. To allow loading children on demand, specify `children : true` in your data. Omit the field for leaf
     * tasks.
     *
     * Note, if the tree store loads data from a remote origin, make sure {@link Core/data/AjaxStore#config-readUrl}
     * is specified, and optionally {@link Core/data/AjaxStore#config-parentIdParamName} is set, otherwise
     * {@link Core/data/Store#function-loadChildren} has to be implemented.
     * @field {Boolean|Object[]|Core.data.Model[]} children
     * @readonly
     * @category Parent & children
     */
    /**
     * Array of tree nodes without any filter applied. On first filter, will take order from sorted `children`, but
     * is not thereafter kept in sorted order, so order should not be relied upon.
     * @member {Core.data.Model[]|null} unfilteredChildren
     * @category Parent & children
     * @private
     */
    /**
     * Array of children unaffected by sorting and filtering, keeps original tree structure
     * @member {Core.data.Model[]|null} orderedChildren
     * @category Parent & children
     * @private
     */
    /**
     * Called during creation to also turn any children into Models joined to the same stores as this model
     * @internal
     * @category Parent & children
     */
    processChildren(stores2 = this.stores) {
      const me = this, { meta } = me;
      me.inProcessChildren = true;
      const children = me.ingestChildren(me.data[me.constructor.childrenField], stores2);
      if (children) {
        const { convertEmptyParentToLeaf } = me.constructor, shouldConvert = convertEmptyParentToLeaf === true || convertEmptyParentToLeaf.onLoad;
        if (shouldConvert ? children.length : Array.isArray(children)) {
          meta.isLeaf = false;
          if (me.children === true) {
            me.children = [];
          } else if (children.length === 0) {
            me.children = children;
            return;
          }
          me.insertChild(children);
        } else if (children === true) {
          meta.isLeaf = false;
          me.children = true;
        } else if (!me.isRoot) {
          meta.isLeaf = me.constructor.convertEmptyParentToLeaf.onLoad;
        }
      }
      me.inProcessChildren = false;
    }
    /**
     * This method returns `true` if this record has all expanded ancestors and is therefore
     * eligible for inclusion in a UI.
     * @param {Core.data.Store} [store] Optional store, defaults to nodes first store
     * @readonly
     * @category Parent & children
     * @returns {Boolean}
     */
    ancestorsExpanded(store = this.firstStore) {
      const { parent } = this;
      return (store == null ? void 0 : store.isTree) ? !parent || parent.isExpanded(store) && parent.ancestorsExpanded(store) : true;
    }
    /**
     * Used by stores to assess the record's collapsed/expanded state in that store.
     * @param {Core.data.Store} store
     * @category Parent & children
     * @returns {Boolean}
     */
    isExpanded(store = this.firstStore) {
      const mapMeta = this.instanceMeta(store.id);
      if (!Object.prototype.hasOwnProperty.call(mapMeta, "collapsed")) {
        mapMeta.collapsed = !this.expanded;
      }
      return !mapMeta.collapsed;
    }
    // A read-only property. It provides the initial state upon load
    // The UI's expanded/collapsed state is in the store's meta map.
    get expanded() {
      return this.data.expanded;
    }
    /**
     * Depth in the tree at which this node exists. First visual level of nodes are at level 0, their direct children at
     * level 1 and so on.
     * @property {Number}
     * @readonly
     * @category Parent & children
     */
    get childLevel() {
      let node = this, ret = -1;
      while (node && !node.isRoot) {
        ++ret;
        node = node.parent;
      }
      return ret;
    }
    /**
     * Is a leaf node in a tree structure?
     * @property {Boolean}
     * @readonly
     * @category Parent & children
     */
    get isLeaf() {
      return this.meta.isLeaf !== false && !this.isRoot;
    }
    /**
     * Returns `true` if this node is the root of the tree
     * @member {Boolean} isRoot
     * @readonly
     * @category Parent & children
     */
    /**
     * Is a parent node in a tree structure?
     * @property {Boolean}
     * @readonly
     * @category Parent & children
     */
    get isParent() {
      return !this.isLeaf;
    }
    /**
     * Returns true for parent nodes with children loaded (there might still be no children)
     * @property {Boolean}
     * @readonly
     * @category Parent & children
     */
    get isLoaded() {
      return this.isParent && Array.isArray(this.children);
    }
    /**
     * Returns values of the persistable tree-defining fields: parentId, parentIndex, orderedParentIndex
     * @readonly
     * @returns {Object|null}
     * @internal
     */
    get hierarchyModificationDataToWrite() {
      const { parentIdField } = this.constructor, result = [parentIdField, "parentIndex", "orderedParentIndex"].reduce((result2, name) => {
        var _a6;
        if (name in this.meta.modified) {
          const field = this.constructor.getFieldDefinition(name);
          if (name === parentIdField) {
            result2[(_a6 = field == null ? void 0 : field.dataSource) != null ? _a6 : parentIdField] = this.getFieldPersistentValue(name);
          } else if (field.persist) {
            result2[field.dataSource] = this.getFieldPersistentValue(name);
          }
        }
        return result2;
      }, {});
      return Object.keys(result).length > 0 ? result : null;
    }
    /**
     * Count all children (including sub-children) for a node (in its `firstStore´)
     * @member {Number}
     * @category Parent & children
     */
    get descendantCount() {
      return this.getDescendantCount();
    }
    /**
     * Count visible (expanded) children (including sub-children) for a node (in its `firstStore`)
     * @member {Number}
     * @category Parent & children
     */
    get visibleDescendantCount() {
      return this.getDescendantCount(true);
    }
    /**
     * Count visible (expanded)/all children for this node, optionally specifying for which store.
     * @param {Boolean} [onlyVisible] Specify `true` to only count visible (expanded) children.
     * @param {Core.data.Store} [store] A Store to which this node belongs
     * @privateparam {Boolean} [ignoreFilters] Specify `true` to ignore filters when counting descendants
     * @returns {Number}
     * @category Parent & children
     */
    getDescendantCount(onlyVisible = false, store = this.firstStore, ignoreFilters = false) {
      let { children } = this;
      if (ignoreFilters && this.unfilteredChildren) {
        children = this.unfilteredChildren;
      }
      if (!children || !Array.isArray(children) || onlyVisible && !this.isExpanded(store)) {
        return 0;
      }
      return children.reduce(
        (count, child) => count + child.getDescendantCount(onlyVisible, store, ignoreFilters),
        children.length
      );
    }
    /**
     * Retrieve all children, excluding filtered out nodes (by traversing sub nodes)
     * @property {Core.data.Model[]}
     * @category Parent & children
     */
    get allChildren() {
      return this.getAllChildren(false);
    }
    /**
     * Retrieve all children, including filtered out nodes (by traversing sub nodes)
     * @property {Core.data.Model[]}
     * @category Parent & children
     */
    get allUnfilteredChildren() {
      return this.getAllChildren(true);
    }
    getAllChildren(unfiltered = false) {
      const children = unfiltered ? this.unfilteredChildren || this.children : this.children;
      if (!children || children === true) {
        return [];
      }
      return children.reduce((all, child) => {
        all.push(child);
        all.push.apply(all, unfiltered ? child.allUnfilteredChildren : child.allChildren);
        return all;
      }, []);
    }
    /**
     * Get the first child of this node
     * @property {Core.data.Model}
     * @readonly
     * @category Parent & children
     */
    get firstChild() {
      const { children } = this;
      return (children == null ? void 0 : children.length) && children[0] || null;
    }
    /**
     * Get the last child of this node
     * @property {Core.data.Model}
     * @readonly
     * @category Parent & children
     */
    get lastChild() {
      const { children } = this;
      return (children == null ? void 0 : children.length) && children[children.length - 1] || null;
    }
    /**
     * Get the previous sibling of this node
     * @member {Core.data.Model} previousSibling
     * @readonly
     * @category Parent & children
     */
    /**
     * Get the next sibling of this node
     * @member {Core.data.Model} nextSibling
     * @readonly
     * @category Parent & children
     */
    /**
     * Returns count of all preceding sibling nodes (including their children).
     * @property {Number}
     * @category Parent & children
     */
    get previousSiblingsTotalCount() {
      let task = this.previousSibling, count = this.parentIndex;
      while (task) {
        count += task.descendantCount;
        task = task.previousSibling;
      }
      return count;
    }
    // This pair of accessors should never return undefined. This is required by `add task below` logic, which is trying
    // to get a task from the ordered tree, before which insert should happen. In the `addToChildren` logic there is a
    // distinction between `null` and `undefined`. `undefined` falls back to reference task in a visual tree, `null`
    // is meant to point out we should insert to the end of the ordered children list. This is really required for
    // `nextOrderedSibling`, another accessor is modified to keep behavior consistent.
    // https://github.com/bryntum/support/issues/7323
    get previousOrderedSibling() {
      var _a6, _b2;
      return (_b2 = (_a6 = this.parent) == null ? void 0 : _a6.orderedChildren[this.orderedParentIndex - 1]) != null ? _b2 : null;
    }
    get nextOrderedSibling() {
      var _a6, _b2;
      return (_b2 = (_a6 = this.parent) == null ? void 0 : _a6.orderedChildren[this.orderedParentIndex + 1]) != null ? _b2 : null;
    }
    get root() {
      var _a6;
      return ((_a6 = this.parent) == null ? void 0 : _a6.root) || this;
    }
    /**
     * Reading this property returns the id of the parent node, if this record is a child of a node.
     *
     * Setting this property appends this record to the record with the passed id **in the same store that this record
     * is already in**.
     *
     * Note that setting this property is **only valid if this record is already part of a tree store**.
     *
     * This is not intended for general use. This is for when a server responds to a record mutation and the server
     * decides to move a record to a new parent. If a `parentId` property is passed in the response data for a record,
     * that record will be moved.
     *
     * @property {Number|String|null}
     * @category Parent & children
     */
    get parentId() {
      return this.parent && !this.parent.isAutoRoot ? this.parent.id : null;
    }
    set parentId(parentId) {
      this.setParentId(parentId);
    }
    setParentId(parentId) {
      const me = this, { firstStore } = me;
      if (firstStore) {
        const { parent } = me, newParent = parentId === null ? firstStore.rootNode : firstStore.getById(parentId);
        if (!(newParent === parent || !parent && !newParent)) {
          if (me.isBatchUpdating) {
            me.meta.batchChanges.parentId = parentId;
          } else if (newParent) {
            newParent.appendChild(me);
          } else {
            parent.removeChild(me);
          }
        }
      } else {
        me.setData(me.constructor.parentIdField, parentId);
      }
    }
    static set parentIdField(parentIdField) {
      this._parentIdField = parentIdField;
      Object.defineProperty(this.prototype, parentIdField, {
        set: function(parentId) {
          this.parentId = parentId;
        },
        get: function() {
          return this.parentId;
        }
      });
    }
    static get parentIdField() {
      return this._parentIdField || "parentId";
    }
    getChildren(options) {
      let result;
      if (options.includeFilteredOutRecords) {
        result = this.unfilteredChildren || this.children;
      } else if (options.useOrderedTree) {
        result = this.orderedChildren;
      } else {
        result = this.children;
      }
      return result;
    }
    /**
     * Traverses all child nodes recursively calling the passed function
     * on a target node **before** iterating the child nodes.
     * @param {Function} fn The function to call
     * @param {Boolean} [skipSelf=false] True to ignore self
     * @param {Object|Boolean} [options] A boolean for includeFilteredOutRecords, or an options object
     * @param {Boolean} [options.includeFilteredOutRecords] True to also include filtered out records
     * @param {Boolean} [options.useOrderedTree] True to traverse unsorted/unfiltered tree
     * @category Parent & children
     */
    traverse(fn2, skipSelf, options) {
      options = fixTraverseOptions(options);
      const me = this, children = me.getChildren(options);
      if (!skipSelf) {
        fn2.call(me, me);
      }
      for (let i = 0, l = children == null ? void 0 : children.length; i < l; i++) {
        children[i].traverse(fn2, false, options);
      }
    }
    /**
     * Traverses all child nodes recursively calling the passed function
     * on child nodes of a target **before** calling it on the node.
     * @param {Function} fn The function to call
     * @param {Boolean} [skipSelf=false] True to skip this node in the traversal
     * @param {Object|Boolean} [options] A boolean for includeFilteredOutRecords, or an options object
     * @param {Boolean} [options.includeFilteredOutRecords] True to also include filtered out records
     * @category Parent & children
     */
    traverseBefore(fn2, skipSelf, options) {
      options = fixTraverseOptions(options);
      const me = this, children = me.getChildren(options);
      for (let i = 0, l = children == null ? void 0 : children.length; i < l; i++) {
        children[i].traverse(fn2, false, options);
      }
      if (!skipSelf) {
        fn2.call(me, me);
      }
    }
    /**
     * Traverses child nodes recursively while fn returns true
     * @param {Function} fn
     * @param {Boolean} [skipSelf=false] True to skip this node in the traversal
     * @param {Object|Boolean} [options] A boolean for includeFilteredOutRecords, or an options object
     * @param {Boolean} [options.includeFilteredOutRecords] True to also include filtered out records
     * @category Parent & children
     * @returns {Boolean}
     */
    traverseWhile(fn2, skipSelf, options) {
      options = fixTraverseOptions(options);
      const me = this;
      let goOn = skipSelf || fn2.call(me, me) !== false;
      if (goOn) {
        const children = me.getChildren(options);
        if (children == null ? void 0 : children.length) {
          goOn = children.every((child) => child.traverseWhile(fn2, false, options));
        }
      }
      return goOn;
    }
    /**
     * Bubbles up from this node, calling the specified function with each node.
     *
     * @param {Function} fn The function to call for each node
     * @param {Boolean} [skipSelf] True to skip this node in the traversal
     * @category Parent & children
     */
    bubble(fn2, skipSelf = false) {
      let me = this;
      if (!skipSelf) {
        fn2.call(me, me);
      }
      while (me.parent) {
        me = me.parent;
        fn2.call(me, me);
      }
    }
    /**
     * Bubbles up from this node, calling the specified function with each node,
     * while the function returns true.
     *
     * @param {Function} fn The function to call for each node
     * @param {Boolean} [skipSelf] True to skip this node in the traversal
     * @category Parent & children
     * @returns {Boolean}
     */
    bubbleWhile(fn2, skipSelf = false) {
      let me = this, goOn = true;
      if (!skipSelf) {
        goOn = fn2.call(me, me);
      }
      while (goOn && me.parent) {
        me = me.parent;
        goOn = fn2.call(me, me);
      }
      return goOn;
    }
    /**
     * Checks if this model contains another model as one of it's descendants
     *
     * @param {Core.data.Model|String|Number} childOrId child node or id
     * @param {Boolean} [skipSelf=false] True to ignore self in the traversal
     * @param {Object|Boolean} [options] A boolean for includeFilteredOutRecords, or an options object
     * @param {Boolean} [options.includeFilteredOutRecords] True to also check filtered out records
     * @category Parent & children
     * @returns {Boolean}
     */
    contains(childOrId, skipSelf = false, options = void 0) {
      if (childOrId && typeof childOrId === "object") {
        childOrId = childOrId.id;
      }
      return !this.traverseWhile((node) => node.id != childOrId, skipSelf, options);
    }
    getTopParent(all) {
      let result;
      if (all) {
        result = [];
        this.bubbleWhile((t) => {
          result.push(t);
          return t.parent && !t.parent.isRoot;
        });
      } else {
        result = null;
        this.bubbleWhile((t) => {
          result = t;
          return t.parent && !t.parent.isRoot;
        });
      }
      return result;
    }
    /**
     * Append a child record(s) to any current children.
     * @param {Core.data.Model|Core.data.Model[]|Object|Object[]} childRecord Array of records/data or a single
     * record/data to append
     * @param {Boolean} [silent] Pass `true` to not trigger events during append
     * @returns {Core.data.Model|Core.data.Model[]|null}
     * @category Parent & children
     */
    appendChild(childRecord, silent = false, options = {}) {
      return this.insertChild(childRecord, null, silent, options);
    }
    /**
     * Insert a child record(s) before an existing child record.
     * @param {Core.data.Model|Core.data.Model[]|Object|Object[]} childRecord Array of records/data or a single
     * record/data to insert
     * @param {Core.data.Model} [before] Optional record to insert before, leave out to append to the end
     * @param {Boolean} [silent] Pass `true` to not trigger events during append
     * @returns {Core.data.Model|Core.data.Model[]|null}
     * @category Parent & children
     */
    insertChild(childRecord, before = null, silent = false, options = {}) {
      var _a6, _b2, _c2, _d, _e, _f, _g, _h, _i;
      const me = this, returnArray = Array.isArray(childRecord);
      if (typeof before === "number") {
        before = (_b2 = (_a6 = me.children) == null ? void 0 : _a6[before]) != null ? _b2 : null;
      }
      if (me.isLinked && !me.meta.isFillingFromMaster) {
        let toInsert = childRecord;
        if (me.firstStore.isChainedTree) {
          if (Array.isArray(childRecord)) {
            toInsert = childRecord.map((r) => {
              var _a7;
              return (_a7 = r.$original) != null ? _a7 : r;
            });
          } else {
            toInsert = (_c2 = childRecord.$original) != null ? _c2 : childRecord;
          }
        }
        return me.$original.insertChild(toInsert, before == null ? void 0 : before.$original, silent, options);
      }
      childRecord = ArrayHelper.asArray(childRecord);
      if (!silent && !me.isLoading && ((_d = me.stores) == null ? void 0 : _d.every((s) => s.trigger("beforeAdd", {
        records: childRecord,
        parent: me
      }) !== false)) === false) {
        return null;
      }
      childRecord = me.ingestChildren(childRecord);
      const index = (_g = (_f = before == null ? void 0 : before.parentIndex) != null ? _f : (_e = me.children) == null ? void 0 : _e.length) != null ? _g : 0, preResult = (_h = me.beforeInsertChild) == null ? void 0 : _h.call(me, childRecord), inserted = me.internalAppendInsert(childRecord, before, silent, options);
      if (inserted.length) {
        me.convertToParent(silent);
      }
      (_i = me.afterInsertChild) == null ? void 0 : _i.call(me, index, childRecord, preResult, inserted, options.orderedBeforeNode);
      return returnArray || !inserted ? inserted : inserted[0];
    }
    /**
     * Converts a leaf node to a parent node, assigning an empty array as its children
     * @param {Boolean} [silent] Pass `true` to not trigger any event
     * @category Parent & children
     */
    convertToParent(silent = false) {
      const me = this, wasLeaf = me.isLeaf;
      me.meta.isLeaf = false;
      if (!me.children) {
        me.children = [];
      }
      if (wasLeaf && !me.root.isLoading && !silent) {
        me.signalNodeChanged({
          isLeaf: {
            value: false,
            oldValue: true
          }
        });
      }
    }
    signalNodeChanged(changes, stores2 = this.stores) {
      stores2 == null ? void 0 : stores2.forEach((s) => {
        s.trigger("update", { record: this, records: [this], changes });
        s.trigger("change", { action: "update", record: this, records: [this], changes });
      });
    }
    tryInsertChild() {
      return this.insertChild(...arguments);
    }
    internalAppendInsert(recordsToInsert, beforeRecord, silent, options) {
      const me = this, { stores: stores2, root, children } = me, { firstStore: rootStore } = root, { parentIdField } = me.constructor, parentId = me.id;
      let isNoop, start, i, newRecordsCloned, oldParentIndices, isMove = {};
      if (!root.isLoading && rootStore) {
        oldParentIndices = [];
        for (i = 0; i < recordsToInsert.length; i++) {
          const newRecord = recordsToInsert[i];
          isMove[newRecord.id] = newRecord.root === root;
          oldParentIndices[i] = newRecord.parentIndex;
        }
      }
      if (beforeRecord && beforeRecord.parent !== me) {
        beforeRecord = null;
      }
      if (children) {
        const insertAt = beforeRecord ? beforeRecord.parentIndex : children.length;
        if (children[start = insertAt] === recordsToInsert[0] || children[start = insertAt - 1] === recordsToInsert[0]) {
          for (isNoop = true, i = 0; isNoop && i < recordsToInsert.length; i++) {
            if (recordsToInsert[i] !== children[start + i]) {
              isNoop = false;
            }
          }
        }
      }
      if (isNoop) {
        return recordsToInsert;
      }
      for (i = 0; i < recordsToInsert.length; i++) {
        const newRecord = recordsToInsert[i], oldParent = newRecord.parent;
        if (rootStore && !root.isLoading) {
          newRecord.traverse((r) => {
            if (r.root === root) {
              isMove[r.id] = true;
            }
          });
        }
        const isCurrentRecordMoved = Boolean(isMove[newRecord.id]), movedWithinParent = oldParent === me && isCurrentRecordMoved;
        if ((oldParent == null ? void 0 : oldParent.removeChild(newRecord, isCurrentRecordMoved, silent, {
          isInserting: true,
          // If we're reordering siblings we should skip index update. Index will be recalculated
          // when we insert node below.
          skipIndexUpdate: movedWithinParent,
          ...options
        })) === false) {
          if (!newRecordsCloned) {
            recordsToInsert = recordsToInsert.slice();
            newRecordsCloned = true;
          }
          recordsToInsert.splice(i--, 1);
        } else {
          newRecord.parent = me;
          newRecord.setData("parentId", parentId);
          const { meta } = newRecord;
          if (meta.modified[parentIdField] === parentId && !oldParent) {
            meta.oldParentId = parentId;
          }
          if (oldParent) {
            meta.oldParentId = oldParent.id;
          }
        }
      }
      if (recordsToInsert.length) {
        if (!Array.isArray(children)) {
          me.children = [];
        }
        if (!Array.isArray(me.orderedChildren)) {
          me.orderedChildren = [];
        }
        const insertAt = me.addToChildren(beforeRecord, recordsToInsert, options);
        stores2 == null ? void 0 : stores2.forEach((store) => {
          if (!store.isChained || store.isChainedTree) {
            recordsToInsert.forEach(
              (record) => (
                // Initialize context for newly added records
                record.joinStore(store)
              )
            );
            store.onNodeAddChild(me, recordsToInsert, insertAt, isMove, silent);
            recordsToInsert.forEach((record, i2) => {
              if (record.meta.oldParentId != null && (!(me.inProcessChildren || me.isLoading) || isMove[record.id])) {
                const toSet = {
                  [parentIdField]: parentId,
                  [me.getDataSource("parentIndex")]: record.parentIndex
                }, wasSet = {}, { modified, oldParentId } = record.meta, oldParentIndex = oldParentIndices[i2];
                delete record.meta.oldParentId;
                if (me.id !== oldParentId) {
                  wasSet[parentIdField] = {
                    value: parentId,
                    oldValue: oldParentId
                  };
                }
                if (record.parentIndex !== oldParentIndex) {
                  wasSet.parentIndex = {
                    value: record.parentIndex,
                    oldValue: oldParentIndex
                  };
                }
                if (modified[parentIdField] === me.id) {
                  Reflect.deleteProperty(modified, parentIdField);
                } else if (!(parentIdField in modified)) {
                  modified[parentIdField] = oldParentId;
                }
                if (isMove[record.id]) {
                  const oldParent = store.getById(oldParentId);
                  if (oldParent.isLeaf && !silent) {
                    oldParent.signalNodeChanged({
                      isLeaf: {
                        value: true,
                        oldValue: false
                      }
                    }, [store]);
                  }
                }
                record.afterChange(toSet, wasSet);
              }
              record.traverse((node) => {
                if (!node.ignoreBag && !node.isLinked) {
                  store.updateModifiedBagForRecord(node);
                }
              });
            });
          }
        });
      }
      return recordsToInsert;
    }
    /**
     * Remove a child record. Only direct children of this node can be removed, others are ignored.
     * @param {Core.data.Model|Core.data.Model[]} childRecords The record(s) to remove.
     * @param {Boolean} [isMove] Pass `true` if the record is being moved within the same store.
     * @param {Boolean} [silent] Pass `true` to not trigger events during remove.
     * @privateparam {Object} [options]
     * @privateparam {Object} [options.isInserting] `true` is passed when removal is part of record inserting (acted on by
     * ModelLink)
     * @returns {Core.data.Model[]} All records (including nested children) removed
     * @category Parent & children
     */
    removeChild(childRecords, isMove = false, silent = false, options = {}) {
      var _a6, _b2, _c2;
      const me = this, allRemovedRecords = [], wasLeaf = me.isLeaf, {
        children,
        stores: stores2
      } = me;
      childRecords = ArrayHelper.asArray(childRecords);
      childRecords = childRecords.filter((r) => r.parent === me);
      if ((_a6 = me.firstStore) == null ? void 0 : _a6.isChainedTree) {
        return me.$original.removeChild(childRecords.map((r) => r.$original), isMove, silent, options);
      }
      if (!silent && stores2) {
        for (const store of stores2) {
          if (!store.isChained && store.trigger("beforeRemove", {
            parent: me,
            records: childRecords,
            isMove
          }) === false) {
            return false;
          }
        }
      }
      const preResult = (_b2 = me.beforeRemoveChild) == null ? void 0 : _b2.call(me, childRecords, isMove);
      for (const childRecord of childRecords) {
        const { parentIdField } = childRecord.constructor, { modified } = childRecord.meta, oldParentId = childRecord.parent ? childRecord.parent.id : null;
        if (!(parentIdField in modified) && !childRecord.isLinked) {
          modified[parentIdField] = oldParentId;
        }
        const index = me.removeFromChildren(childRecord, options);
        stores2 == null ? void 0 : stores2.forEach((store) => {
          if (!store.isChained) {
            const { isRemoving } = store;
            store.isRemoving = true;
            const removedRecords = store.onNodeRemoveChild(me, [childRecord], index, { isMove, silent, ...options });
            ArrayHelper.chunkedPush(allRemovedRecords, removedRecords);
            store.isRemoving = isRemoving;
          }
        });
        if (!isMove) {
          childRecord.parent = childRecord.parentIndex = childRecord.unfilteredIndex = childRecord.nextSibling = childRecord.previousSibling = null;
          childRecord.data[parentIdField] = null;
        }
      }
      if ((me.unfilteredChildren || children).length === 0 && me.constructor.convertEmptyParentToLeaf.onRemove && !me.isRoot) {
        me.meta.isLeaf = true;
      }
      if (me.isLeaf !== wasLeaf && !silent && !isMove) {
        me.signalNodeChanged({
          isLeaf: {
            value: true,
            oldValue: false
          }
        });
      }
      (_c2 = me.afterRemoveChild) == null ? void 0 : _c2.call(me, childRecords, preResult, isMove);
      return allRemovedRecords;
    }
    clearParentId() {
      const me = this, { parentIdField } = me.constructor;
      Reflect.deleteProperty(me.data, parentIdField);
      Reflect.deleteProperty(me.originalData, parentIdField);
      if (me.meta.modified) {
        Reflect.deleteProperty(me.meta.modified, parentIdField);
      }
    }
    /**
     * Replaces all child nodes with the new node set.
     * @param {Core.data.Model|Core.data.Model[]} childRecords The new child record set.
     * @returns {Core.data.Model[]}
     * @category Parent & children
     */
    replaceChildren(childRecords) {
      this.clearChildren();
      this.data[this.constructor.childrenField] = childRecords;
      this.processChildren();
      return this.children;
    }
    /**
     * Removes all child nodes from this node.
     * @param {Boolean} [silent=false] Pass `true` to not fire Store events during the remove.
     * @returns {Core.data.Model[]}
     * @category Parent & children
     */
    clearChildren(silent = false) {
      const me = this, { stores: stores2 } = me, children = me.unfilteredChildren || me.children;
      me.children = [];
      me.orderedChildren = [];
      if (children && children !== true && stores2) {
        stores2.forEach((store) => {
          if (!store.isChained) {
            store.onNodeRemoveChild(me, children, 0, { unfiltered: true, silent });
          }
        });
      }
      if (me.unfilteredChildren) {
        me.unfilteredChildren = [];
      }
      const { convertEmptyParentToLeaf } = me.constructor;
      if (convertEmptyParentToLeaf === true || convertEmptyParentToLeaf.onRemove) {
        me.meta.isLeaf = true;
      }
    }
    /**
     * Removes all records from the rootNode
     * @private
     */
    clear() {
      var _a6;
      const me = this, { stores: stores2 } = me, children = (_a6 = me.unfilteredChildren || me.children) == null ? void 0 : _a6.slice();
      if (!me.isRoot || !children) {
        return;
      }
      for (const store of stores2) {
        if (!store.isChained) {
          if (store.trigger("beforeRemove", {
            parent: me,
            records: children,
            isMove: false,
            removingAll: true
          }) === false) {
            return false;
          }
        }
      }
      me.children.length = me.orderedChildren.length = 0;
      if (me.unfilteredChildren) {
        me.unfilteredChildren.length = 0;
      }
      stores2.forEach((store) => {
        children.forEach((child) => {
          if (child.stores.includes(store)) {
            child.unjoinStore(store);
          }
          child.parent = child.parentIndex = child.nextSibling = child.previousSibling = null;
        });
        store.storage.suspendEvents();
        store.storage.clear();
        store.storage.resumeEvents();
        store.added.clear();
        store.modified.clear();
        store.trigger("removeAll");
        store.trigger("change", { action: "removeall" });
      });
    }
    updateChildrenIndices(children, indexName, silent = false) {
      var _a6, _b2;
      let previousSibling = null;
      for (let i = 0; i < children.length; i++) {
        const child = children[i], oldValue = child[indexName];
        if (indexName === "parentIndex" || indexName === "orderedParentIndex") {
          if (oldValue === void 0 || silent) {
            child.setData(indexName, i);
          } else if (oldValue !== i) {
            (_a6 = child.stores) == null ? void 0 : _a6.forEach((s) => {
              s.suspendSortOnUpdate();
              s.suspendFilterOnUpdate();
            });
            child.set(indexName, i, true);
            (_b2 = child.stores) == null ? void 0 : _b2.forEach((s) => {
              s.resumeSortOnUpdate(false);
              s.resumeFilterOnUpdate(false);
            });
          }
        } else {
          child[indexName] = i;
        }
        if (indexName === "parentIndex") {
          child.previousSibling = previousSibling;
          if (previousSibling) {
            previousSibling.nextSibling = child;
          }
          if (i === children.length - 1) {
            child.nextSibling = null;
          }
          previousSibling = child;
        }
      }
    }
    addToChildren(beforeRecord, newRecords, options = {}) {
      var _a6, _b2;
      const me = this, configs = [
        [me.children, "parentIndex", beforeRecord],
        [me.unfilteredChildren, "unfilteredIndex", beforeRecord],
        [
          me.orderedChildren,
          "orderedParentIndex",
          // Distinguish null and undefined here. `null` means add to the end of the ordered tree
          (options == null ? void 0 : options.orderedBeforeNode) === void 0 ? (options == null ? void 0 : options.orderedParentIndex) !== void 0 ? me.orderedChildren[(_a6 = options.orderedParentIndex.index) != null ? _a6 : options.orderedParentIndex] : beforeRecord : options == null ? void 0 : options.orderedBeforeNode
        ]
      ];
      for (const config of configs) {
        const [children, indexName, beforeRecord2] = config;
        if (children) {
          let index;
          if (beforeRecord2) {
            index = children.indexOf(beforeRecord2);
            if (index === -1) {
              index = children.length;
            }
          } else {
            index = children.length;
          }
          config.push(index);
          ArrayHelper.chunkedInsert(children, index, newRecords);
          if (!((options == null ? void 0 : options.skipIndexUpdate) || ((_b2 = options == null ? void 0 : options[indexName]) == null ? void 0 : _b2.skip))) {
            me.updateChildrenIndices(children, indexName);
          }
        }
      }
      return configs[0][3];
    }
    removeFromChildren(childRecord, options) {
      var _a6;
      const configs = [
        [this.children, "parentIndex"],
        [this.unfilteredChildren, "unfilteredIndex"],
        [this.orderedChildren, "orderedParentIndex"]
      ];
      for (const config of configs) {
        const [children, indexName] = config;
        if (children) {
          const index = children.indexOf(childRecord);
          config.push(index);
          if (index > -1) {
            children.splice(index, 1);
            if (!((options == null ? void 0 : options.skipIndexUpdate) || ((_a6 = options == null ? void 0 : options[indexName]) == null ? void 0 : _a6.skip))) {
              this.updateChildrenIndices(children, indexName);
            }
          }
        }
      }
      return configs[0][2];
    }
    /**
     * Iterates orderedChildren array to apply sorting order according to `orderedParentIndex`.
     * Normally sorting is not required because order is maintained on append/insert. But is useful
     * when pasting number of records to restore their original order.
     * @param {Boolean} [deep=true] True to dive into children. False to sort own children.
     * @param {Boolean} [usePreviousOrder=false] Enable to use previous value of `orderedParentIndex`.
     * @returns {Set} Returns Set of moved nodes which require WBS update
     * @private
     */
    sortOrderedChildren(deep = true, usePreviousOrder = false) {
      const me = this, movedNodes = [];
      if (!me.isLeaf) {
        me.orderedChildren.sort((a, b) => {
          var _a6, _b2;
          if (usePreviousOrder) {
            const aPrevIndex = (_a6 = a.meta.modified.orderedParentIndex) != null ? _a6 : a.orderedParentIndex, bPrevIndex = (_b2 = b.meta.modified.orderedParentIndex) != null ? _b2 : b.orderedParentIndex, result = aPrevIndex - bPrevIndex;
            if (result !== 0) {
              movedNodes.push(a);
              movedNodes.push(b);
            }
            return result;
          } else {
            return a.orderedParentIndex - b.orderedParentIndex;
          }
        });
        if (deep) {
          me.orderedChildren.forEach((child) => {
            const childNodes = child.sortOrderedChildren(deep, usePreviousOrder);
            ArrayHelper.chunkedPush(movedNodes, childNodes);
          });
        }
        me.updateChildrenIndices(me.orderedChildren, "orderedParentIndex", true);
      }
      return new Set(movedNodes);
    }
    sortVisibleChildren(deep = true, sorterFn) {
      const me = this;
      if (!me.isLeaf) {
        me.children.sort(sorterFn);
        if (deep) {
          me.orderedChildren.forEach((child) => child.sortVisibleChildren(deep, sorterFn));
        }
        me.updateChildrenIndices(me.children, "parentIndex", true);
      }
    }
    sortUnfilteredChildren(deep = true, sorterFn) {
      const me = this;
      if (!me.isLeaf && me.unfilteredChildren) {
        me.unfilteredChildren.sort(sorterFn);
        if (deep) {
          me.orderedChildren.forEach((child) => child.sortUnfilteredChildren(deep, sorterFn));
        }
        me.updateChildrenIndices(me.unfilteredChildren, "unfilteredIndex", true);
      }
    }
    unjoinStore(store, isReplacing = false) {
      var _a6;
      const me = this;
      if (me.unfilteredChildren) {
        me.children = me.unfilteredChildren.slice();
        me.unfilteredChildren = null;
      }
      (_a6 = super.unjoinStore) == null ? void 0 : _a6.call(this, store, isReplacing);
    }
  }, __publicField(_a5, "$name", "TreeNode"), _a5;
};

// ../Core/lib/Core/data/mixin/ModelLink.js
var propertyOverrides = {
  id: 1,
  internalId: 1,
  stores: 1,
  unjoinedStores: 1,
  parentIndex: 1,
  parent: 1,
  unfilteredChildren: 1,
  children: 1,
  orderedChildren: 1,
  parentId: 1,
  previousSibling: 1,
  nextSibling: 1,
  unfilteredIndex: 1
};
var proxyConfig = {
  get(target, prop) {
    switch (prop) {
      case "proxyMeta":
        return this.proxyMeta;
      case "constructor":
        return target.constructor;
      case "setData":
        return this.setDataOverride;
      case "set":
        return this.setOverride;
      case "instanceMeta":
        return this.instanceMetaOverride;
    }
    if (propertyOverrides[prop]) {
      return this.proxyMeta.data[prop];
    }
    return Reflect.get(target, prop, this.proxyRecord);
  },
  set(target, prop, value2, proxyRecord) {
    if (prop === "parentId") {
      proxyRecord.setParentId(value2);
    } else if (propertyOverrides[prop]) {
      this.proxyMeta.data[prop] = value2;
    } else {
      target[prop] = value2;
    }
    return true;
  },
  // The instanceMeta has to be unique for this link instance.
  // On first reference, we create a clone of the original's instanceMeta
  instanceMetaOverride(instanceOrId) {
    const { proxyMeta } = this, id2 = instanceOrId.id || instanceOrId, map2 = proxyMeta.map || (proxyMeta.map = ObjectHelper.clone(proxyMeta.originalRecord.instanceMeta(id2)));
    return map2[id2] || (map2[id2] = {});
  },
  // Override setData & set to reroute parentIndex updates
  setDataOverride(toSet, value2) {
    if (toSet === "parentIndex") {
      this.proxyMeta.data.parentIndex = value2;
    } else if (toSet === "parentId") {
      this.proxyMeta.data.parentId = value2;
    } else {
      this.proxyMeta.originalRecord.setData(toSet, value2);
    }
  },
  setOverride(field, value2, ...args) {
    if (field === "parentIndex") {
      this.proxyMeta.data.parentIndex = value2;
    } else {
      this.proxyMeta.originalRecord.set(field, value2, ...args);
    }
  }
};
var ModelLink_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    /**
     * Creates a proxy record (using native Proxy) linked to this record (the original). The proxy records shares most
     * data with the original, except for its `id` (which is always generated), and ordering fields such as
     * `parentIndex` and `parentId` etc.
     *
     * Any change to the proxy record will be reflected on the original, and vice versa. A proxy record is not meant to
     * be persisted, only the original record should be persisted. Thus, proxy records are not added to stores change
     * tracking (added, modified and removed records).
     *
     * Removing the original record removes all proxies.
     *
     * Creating a proxy record allows a Store to seemingly contain the record multiple times, something that is
     * otherwise not possible. It also allows a record to be used in both a tree store and in a flat store.
     *
     * <div class="note">Note that not all UI features support linked records</div>
     *
     * @returns {Proxy} Proxy record linked to the original record
     * @typings {typeof Proxy}
     * @category Misc
     */
    link() {
      if (this.isLinked) {
        return this.$original.link();
      }
      const me = this, useConfig = {
        ...proxyConfig,
        // Data not shared with the original record
        proxyMeta: {
          originalRecord: me,
          data: {
            // Used to use UUIDs, but generating them is too slow
            id: `${me.id}_link_${me.constructor._internalIdCounter}`,
            // Using Model._internalIdCounter++ here causes issues with UMD bundle
            // seems it can't understand Model imports ModelLink which imports Model
            internalId: me.constructor._internalIdCounter++,
            stores: [],
            unjoinedStores: [],
            // make separate children array if original record has them
            ...me.children ? {
              unfilteredChildren: [],
              children: [],
              orderedChildren: []
            } : {}
          }
        }
      }, proxyRecord = new Proxy(me, useConfig);
      useConfig.proxyRecord = proxyRecord;
      (me.meta.linkedRecords || (me.meta.linkedRecords = [])).push(proxyRecord);
      return proxyRecord;
    }
    /**
     * Is this record linked to another record?
     * @member {Boolean}
     * @readonly
     * @category Linked records
     */
    get isLinked() {
      var _a6;
      return Boolean((_a6 = this.proxyMeta) == null ? void 0 : _a6.originalRecord);
    }
    /**
     * Are other records linked to this record?
     * @member {Boolean}
     * @readonly
     * @category Linked records
     */
    get hasLinks() {
      return Boolean(!this.proxyMeta && this.$links.length);
    }
    /**
     * Get links to this record.
     * @member {Core.data.Model[]}
     * @readonly
     * @category Linked records
     */
    get recordLinks() {
      return this.$links;
    }
    /**
     * Get the original record this record is linked to.
     * @member {Core.data.Model[]}
     * @readonly
     * @category Linked records
     */
    get originalRecord() {
      return this.$original;
    }
    // Returns the original if in store, or any link that is in the store.
    getStoreLink(store) {
      if (store.includes(this)) {
        return this;
      }
      for (const link of this.$links) {
        if (store.includes(link)) {
          return link;
        }
      }
      return null;
    }
    // Logic to remove a link shared between removing in a flat store and a tree store
    removeLink(link, records = null, silent = false) {
      if (link.hasLinks) {
        for (const linked of link.$links.slice()) {
          if (records) {
            ArrayHelper.include(records, linked);
          } else {
            linked.remove(silent);
          }
        }
      } else if (link.isLinked) {
        ArrayHelper.remove(link.$original.$links, link);
      }
    }
    // Overrides beforeRemove in Model, to remove all linked records when original record is removed.
    beforeRemove(records) {
      this.removeLink(this, records);
    }
    // Overrides removeChild in TreeNode, to remove the original node and all linked nodes when either a linked or
    // original node is removed.
    removeChild(childRecords, isMove, silent, options) {
      if (!(options == null ? void 0 : options.isInserting)) {
        childRecords = ArrayHelper.asArray(childRecords);
        for (const child of childRecords) {
          this.removeLink(child, null, silent);
        }
      }
      return super.removeChild(childRecords, isMove, silent, options);
    }
    // Convenience getter for code keying by id that needs to work with both link and original
    get $originalId() {
      return this.$original.id;
    }
    // Convenience getter to retrieve linked records
    get $links() {
      var _a6;
      return (_a6 = this.meta.linkedRecords) != null ? _a6 : [];
    }
  }, __publicField(_a5, "$name", "ModelLink"), __publicField(_a5, "isModel", true), _a5;
};

// ../Core/lib/Core/mixin/Factoryable.js
var { defineProperty: defineProperty4 } = Reflect;
var ownerSymbol = Symbol("owner");
var typeSplitRe = /[\s,]+/;
var Factoryable_default = (Target) => {
  var _b2;
  return _b2 = class extends (Target || Base) {
    static get declarable() {
      return [
        /**
         * This property getter returns options that control the factory process. This property getter must be
         * defined by the class that mixes in `Factoryable` in order to initialize the factory properly.
         * ```
         *  static get factoryable() {
         *      return {
         *          defaultType : 'default'
         *      };
         *  }
         * ```
         * If there are no special options to provide, this method can return nothing (`undefined`):
         * ```
         *  static get factoryable() {
         *      // initialize the factory with all default options
         *  }
         * ```
         * @static
         * @member {Object} factoryable
         * @property {Boolean} [factoryable.caseless=true] Specify `false` to use case-sensitive type names. The
         * default is to ignore case.
         * @property {String} [factoryable.defaultType=null] The default type to create when a config object has
         * no `typeKey` property.
         * @property {Function|Function[]} [factoryable.extends] One or more classes that mix in `Factoryable` to
         * use for resolving type names when a type name is not found in this factory.
         * @property {String} [factoryable.typeKey='type'] The name of the property in a config object that holds
         * the type name.
         * @internal
         */
        "factoryable",
        /**
         * One or more additional type name aliases for this class. This can be useful for renaming and maintaining
         * a previous type name.
         * ```
         *  class Fit extends Layout {
         *      static type = 'fit';
         *
         *      static alias = 'fill';
         *  }
         * ```
         * @static
         * @member {String|String[]} alias
         * @internal
         */
        "alias",
        /**
         * The (canonical) type name for this class by which instances can be created using the static
         * {@link #function-create-static create()} method.
         * @static
         * @member {String} type
         */
        "type"
      ];
    }
    /**
     * Registers a class (`cls`) associated with the given `type`.
     * @param {String|String[]} type A string, array of strings or a comma-separated string containing the type names
     * for the specified `cls` class.
     * @param {Function} cls The class (constructor function)
     * @param {Boolean} [replace] Pass `true` to overwrite existing registered types. Otherwise, this method will throw
     * an exception if the `type` is already registered with this factory.
     * @internal
     */
    static register(type, cls, replace = globalThis.__BRYNTUM_EXAMPLE || ((_a5) => (_a5 = globalThis.DocsBrowser) == null ? void 0 : _a5.isDocsBrowser)()) {
      const { factoryable } = this.initClass(), { caseless, registry: registry3 } = factoryable, types = StringHelper.split(type, typeSplitRe);
      for (let lower, name, i = 0; i < types.length; ++i) {
        name = types[i];
        lower = caseless ? name.toLowerCase() : name;
        if (!replace && lower in registry3) {
          throw new Error(`Type "${name}" already registered with ${factoryable.class.name} factory`);
        }
        registry3[name] = registry3[lower] = cls.initClass();
      }
    }
    /**
     * Returns `true` if the passed instance is of the passed type or of a derived class.
     * @param {Object} instance The object to test.
     * @param {String} type The type to test against
     */
    static isA(instance, type) {
      return this.isType(instance, type, true);
    }
    /**
     * Returns `true` if the passed instance is of the passed type.
     * @param {Object} instance The object to test.
     * @param {String} type The type to test against
     * @param {Boolean} [deep] Pass `true` to return `true` if the class is a subclass of the passed type.
     */
    static isType(instance, type, deep) {
      const { factoryable } = this, { caseless, registry: registry3 } = factoryable, typeCls = registry3[caseless ? type.toLowerCase() : type];
      return typeCls ? deep ? instance instanceof typeCls : instance.constructor === typeCls : false;
    }
    static setupAlias(cls) {
      cls.register(cls.alias, cls);
    }
    static setupFactoryable(cls, meta) {
      const superClass = meta.super.class;
      let { factoryable } = cls;
      factoryable = {
        caseless: true,
        defaultType: null,
        extends: superClass.factoryable ? [superClass] : null,
        typeKey: "type",
        ...factoryable
      };
      factoryable.class = cls;
      factoryable.registry = /* @__PURE__ */ Object.create(null);
      if (factoryable.extends && !Array.isArray(factoryable.extends)) {
        factoryable.extends = [factoryable.extends];
      }
      defineProperty4(cls, "factoryable", {
        get() {
          return factoryable;
        }
      });
    }
    static setupType(cls, meta) {
      const { type } = cls;
      cls.register(type, cls, meta.replaceType);
      defineProperty4(cls.prototype, "type", {
        get: () => type,
        set: (value2) => {
          if (value2 !== type) {
            throw new Error("Can not mutate `type` config to different value");
          }
        }
      });
    }
    /**
     * Creates an instance from this factory, given the type name or a config object.
     * @param {String|Object} config The type name string or config object.
     * @param {String|Function|Object} [options] Creation options (for details see {@link #function-reconfigure-static}).
     * @returns {Object}
     */
    static create(config, options) {
      return this.reconfigure(null, config, options);
    }
    /**
     * Reconfigures an optional existing instance based on the provided config and returns the correctly configured
     * instance. This will be the `existingInstance` if the `config` does not specify a different type.
     *
     * If `config` is `null` (or simply falsy), this method will destroy the `existingInstance` (if any) and return
     * `null`.
     *
     * If there is no `existingInstance`, the config must specify a type. That is, it must be a string (the type name)
     * or an object containing a `type` property, the `defaultType` must be provided or the factory itself must have
     * a `defaultType` specified (see {@link #property-factoryable-static}).
     *
     * When an `existingInstance` is provided and a type is specified, the instance will be reconfigured via `setConfig`
     * if it is of that type. Otherwise, the `existingInstance` is destroyed (if it is owned by the `options.owner`)
     * and a new instance of the correct type is created.
     *
     * @param {Object} existingInstance The instance to reconfigure. This can be `null`.
     *
     * @param {String|Object} config The type name string or config object.
     *
     * @param {String|Function|Object} [options] Additional options to control the reconfiguration process. If this
     * value is a string or a class constructor, it treated as `options.type`. If this value is a class instance, it
     * is used as the `options.owner`. If this is a function, it is treated as `options.setup`. NOTE: functions declared
     * using the `function` keyword are equivalent to class constructors. Use an arrow function or a class method to
     * avoid this when a `setup` function is intended.
     *
     * @param {String|Function} [options.type] The default type to use if the `config` object does not specify a type.
     *
     * @param {Object} [options.owner] The owner of any created instances. If the `existingInstance` is being replaced,
     * this value determines if the instance will be destroyed.
     *
     * @param {Object} [options.defaults] A config object of default values to use when creating a new instance.
     *
     * @param {Function|String} [options.setup] A function or the name of a method (on the `options.owner`) to call
     * prior to creating a new instance. It is passed the config object that will be used to create the instance. If a
     * truthy value is returned, that value is passed to the constructor instead of the provided config object.
     *
     * @param {Function|String} [options.transform] A function or the name of a method (on the `options.owner`) to call
     * with the raw config object prior to processing and the value it returns replaces the raw value. This function is
     * used to transform strings or arrays (for example) into proper config objects.
     *
     * @param {Function|String} [options.cleanup] A function or the name of a method (on the `options.owner`) to call
     * prior to destroying the `existingInstance`. The `existingInstance` is passed as the sole argument.
     *
     * @returns {Object} The reconfigured instance (either `existingInstance` or a new instance of the desired type)
     */
    static reconfigure(existingInstance, config, options) {
      const me = this, { factoryable } = me, { typeKey } = factoryable;
      let defaultType = options, cleanup, defaults, mergeType, owner, prepared, setup, t, transform, type;
      if (options && !ObjectHelper.isClass(options)) {
        defaultType = null;
        t = typeof options;
        if (t === "function") {
          setup = options;
        } else if (t === "string") {
          defaultType = options;
        } else if (ObjectHelper.isObject(options)) {
          cleanup = options.cleanup;
          defaults = options.defaults;
          owner = options.owner;
          setup = options.setup;
          defaultType = options.type;
          transform = options.transform;
        } else {
          owner = options;
        }
      }
      if (transform) {
        config = typeof transform === "string" ? owner[transform](config) : transform(config);
      }
      type = config;
      if (typeof type === "string") {
        config = {};
      } else if (config) {
        if (config === true) {
          config = {};
        }
        if (!ObjectHelper.isObject(config)) {
          if (owner && config !== existingInstance && (existingInstance == null ? void 0 : existingInstance[ownerSymbol]) === owner) {
            typeof cleanup === "string" ? owner[cleanup](existingInstance) : cleanup == null ? void 0 : cleanup(existingInstance);
            existingInstance.destroy();
          }
          return config;
        }
        type = config[typeKey];
      }
      type = type && me.resolveType(type);
      if (existingInstance) {
        if (config && (!type || existingInstance.constructor === type)) {
          if (typeKey in config) {
            config = ObjectHelper.assign({}, config);
            delete config[typeKey];
          }
          existingInstance.setConfig(config);
          return existingInstance;
        }
        if (owner && existingInstance[ownerSymbol] === owner) {
          typeof cleanup === "string" ? owner[cleanup](existingInstance) : cleanup == null ? void 0 : cleanup(existingInstance);
          existingInstance.destroy();
        }
      }
      if (config) {
        if (defaults) {
          if (!(mergeType = type)) {
            if (!(mergeType = defaults[typeKey] || defaultType || factoryable.defaultType)) {
              throw new Error(`No default mergeType defined for ${factoryable.class.name} factory`);
            }
            mergeType = me.resolveType(mergeType);
          }
          if (mergeType) {
            config = mergeType.mergeConfigs(defaults, config);
          }
        }
        if (setup) {
          prepared = typeof setup === "string" ? owner[setup](config, type, defaults) : setup(config, type, defaults);
          if (prepared === null) {
            return prepared;
          }
          config = prepared || config;
        }
        if (!type) {
          if (!(type = config[typeKey] || defaultType || factoryable.defaultType)) {
            throw new Error(`No default type defined for ${factoryable.class.name} factory`);
          }
          type = me.resolveType(type);
        }
        if (defaults && !mergeType) {
          config = type.mergeConfigs(defaults, config);
        }
        if (typeKey in config) {
          config = ObjectHelper.assign({}, config);
          delete config[typeKey];
        }
        config = new type(config);
        if (owner) {
          config[ownerSymbol] = owner;
        }
      }
      return config || null;
    }
    /**
     * This method returns the constructor of the class registered for the given type name.
     * @param {String} type The type name to look up.
     * @param {Boolean} [optional] Pass `true` to return `null` if `type` is not found instead of throwing an exception.
     * @returns {Function}
     */
    static resolveType(type, optional) {
      if (typeof type !== "string") {
        return type;
      }
      const { factoryable } = this, bases = factoryable.extends;
      let result = factoryable.registry[factoryable.caseless ? type.toLowerCase() : type], i;
      for (i = 0; !result && bases && i < bases.length; ++i) {
        result = bases[i].resolveType(
          type,
          /* optional = */
          true
        );
      }
      if (!result && !optional) {
        throw new Error(`Invalid type name "${type}" passed to ${factoryable.class.name} factory`);
      }
      return result;
    }
  }, __publicField(_b2, "$name", "Factoryable"), _b2;
};

// ../Core/lib/Core/util/FormulaProvider.js
var classNameRE = /^([A-Z]+)(?:F|f)ormula/;
var allMatchRE = /^(.*)$/;
var specialFieldNames = {
  this: 1,
  data: 1
};
var recordToObject = (record) => {
  var _a5;
  return (_a5 = record.fields) == null ? void 0 : _a5.reduce((a, f) => {
    if (f.persist) {
      const v = record.get(f.name);
      if (v) {
        a[f.name] = String(record.get(f.name));
      }
    }
    return a;
  }, {});
};
var _FormulaProvider = class _FormulaProvider extends Base.mixin(Factoryable_default, Events_default) {
  constructor() {
    super(...arguments);
    __publicField(this, "fieldMatchRE", /\$([a-zA-Z\-_]+)/g);
    __publicField(this, "fieldDelimiterRE", /[^a-zA-Z\-$_]/);
  }
  changeHeaders(headers) {
    return ObjectHelper.clone(headers);
  }
  updateInputField(inputField, was) {
    var _a5;
    (_a5 = this.inputListenerRemover) == null ? void 0 : _a5.call(this);
    if (inputField) {
      this.inputListenerRemover = inputField.ion({
        thisObj: this,
        input: "onFieldInput",
        change: "onFieldChange"
      });
    }
    return this.owner = inputField;
  }
  onFieldInput() {
    var _a5, _b2, _c2, _d;
    const me = this, { inputField } = me, modelClass = ((_a5 = me.store) == null ? void 0 : _a5.modelClass) || (me.record || inputField.record).constructor, fieldNames = me.fieldNames || (me.fieldNames = Object.keys(specialFieldNames).concat(Object.keys(modelClass.fieldMap)));
    if (inputField.formulaProvider === me) {
      const currentToken = (_b2 = inputField.getCurrentToken(me.fieldDelimiterRE)) == null ? void 0 : _b2.token, currentField = (currentToken == null ? void 0 : currentToken.startsWith("$")) ? currentToken.substring(1) : null, nonMatches = (_d = (_c2 = inputField.value.matchAll(me.fieldMatchRE)) == null ? void 0 : _c2.reduce) == null ? void 0 : _d.call(_c2, (v, [, m]) => {
        if (m === currentField) {
          if (!fieldNames.some((n) => n.startsWith(m))) {
            v.push(m);
          }
        } else if (!specialFieldNames[m] && !modelClass.fieldMap[m]) {
          v.push(m);
        }
        return v;
      }, []);
      if (nonMatches == null ? void 0 : nonMatches.length) {
        inputField.setError(`Undefined data field(s) ${nonMatches.join(",")}`, false, true);
      } else {
        inputField.clearError();
      }
      const handleFormulaInput = me.resolveCallback(me.handleFormulaInput, me, false);
      handleFormulaInput == null ? void 0 : handleFormulaInput.handler.call(handleFormulaInput.thisObj, inputField, this);
    }
  }
  /**
   * Called when the {@link #config-inputField} fires its change event.
   *
   * If this `FormulaProvider` was created from an explicit `type`, or the value matches a formula format, then a
   * formula value is created from the {@link #property-inputField}'` value with the following substitutions made:
   *
   *  - `$fieldName` tokens replaced by the value of the named field from the configured {@link #property-record}
   *  - `$this` token replaced by a JSON representation of the string values of the {@link #property-record}
   *  - `$data` token replaced by a JSON representation of all records in the configured {@link #config-store}
   *
   * The resulting string is passed to the {@link #config-handleFormulaChange} callback.
   *
   * This may be called to trigger the change processing at any time. For instance if the record is mutated, the
   * app may wish to cause the change processing to execute.
   * @internal
   */
  onFieldChange() {
    var _a5;
    const me = this, { inputField } = me, { value: value2 } = inputField, formulaSource = me.formulaRE ? (_a5 = value2.trim().match(me.formulaRE || me.constructor.formulaRE || allMatchRE)) == null ? void 0 : _a5[1] : value2, handleFormulaChange = me.resolveCallback(me.handleFormulaChange, me, false);
    if (formulaSource && handleFormulaChange) {
      const formula = me.createFormula(formulaSource, me.record || inputField.record, me.store);
      handleFormulaChange == null ? void 0 : handleFormulaChange.handler.call(handleFormulaChange.thisObj, me, formula, inputField);
    }
  }
  /**
   * Processes the passed prompt using the configured {@link #config-generateContent} or the configured
   * {@link #config-url} and returns the string result.
   *
   * Note that this is an `async` function and the return value will need to be `await`ed.
   * @param {String} prompt
   * @returns {String}
   * @async
   */
  async calculate(prompt) {
    return this.callback(this.generateContent, this, [prompt]);
  }
  updatePrefix(prefix) {
    this.formulaRE = new RegExp(`^=${prefix}\\((.+)\\)$`);
  }
  updateUrl(url) {
    this.generateContent = url ? this.getRemoteContent : null;
  }
  async getRemoteContent(prompt) {
    let result;
    try {
      const me = this, { method, headers } = me, body = {
        ...me.body
      };
      ObjectHelper.setPath(body, me.paramName, prompt);
      const response = await AjaxHelper.fetch(me.url, ObjectHelper.assign({
        credentials: void 0,
        method,
        headers,
        body: JSON.stringify(body)
      }, me.fetchOptions));
      if (!response.ok) {
        me.trigger("formulaNetworkError", {
          response,
          bubbles: true
        });
        return "";
      }
      result = await response.json();
    } catch (error) {
      console.error("Error:", error);
    }
    return ObjectHelper.getPath(result, this.responseField);
  }
  createFormula(formulaSource, record, store) {
    const event = {
      formula: formulaSource.replaceAll(this.fieldMatchRE, (_, fieldName) => {
        var _a5;
        switch (fieldName) {
          case "this":
            return JSON.stringify(recordToObject(record));
          case "data":
            return JSON.stringify(store.allRecords.map((r) => recordToObject(r)));
          default:
            return (_a5 = record.get(fieldName)) != null ? _a5 : "";
        }
      })
    };
    this.trigger("formulaChange", event);
    return event.formula;
  }
  get store() {
    var _a5;
    return this._store || ((_a5 = this.record) == null ? void 0 : _a5.firstStore);
  }
  static get(prefix, config) {
    var _a5;
    let result = (_a5 = this.map) == null ? void 0 : _a5.get(prefix);
    if (result && config) {
      result = new result(config);
    }
    return result;
  }
  static initClass() {
    var _a5, _b2;
    const fp = _FormulaProvider, me = this, formulaPrefix = me === fp ? "FORMULA" : ((_b2 = (_a5 = me.$name) == null ? void 0 : _a5.match(classNameRE)) == null ? void 0 : _b2[1]) || me.formulaPrefix;
    if (formulaPrefix) {
      if (!me.formulaPrefix) {
        me.formulaPrefix = formulaPrefix;
      }
      ;
      me.inputPrefixRE = new RegExp(`^=${formulaPrefix}\\(.*`);
      me.formulaRE = new RegExp(`^=${formulaPrefix}\\((.+)\\)`);
      (fp.map || (fp.map = /* @__PURE__ */ new Map())).set(formulaPrefix, me);
      const allKeys = [...fp.map.keys()].map((k) => k).join("|");
      fp.inputPrefixRE = new RegExp(`^=(${allKeys})\\(.*`);
    }
    return super.initClass();
  }
};
__publicField(_FormulaProvider, "$name", "FormulaProvider");
__publicField(_FormulaProvider, "type", "formula");
__publicField(_FormulaProvider, "configurable", {
  callOnFunctions: true,
  /**
   * The Record from which this formula provider fetches field definitions and values
   * @prp {Core.data.Model}
   */
  record: null,
  /**
   * The store from which this formula provider fetches field definitions. Defaults to the {@link #config-record}'s first store.
   * @config {Core.data.Store} [store]
   */
  store: null,
  /**
   * The input field which provides the formula.
   *
   * This field becomes this object's `owner`, so that it can be used as the `thisObj` for callbacks.
   *
   * If this `FormulaProvider` was created from a known `type`, for example the input field was configured
   * with
   *
   * ```javascript
   *     formulaConfig : {
   *         type   : 'aiformula',
   *         url    : 'https://aimodule.com/',
   *         apiKey : 'XXXXXXX'
   *     }
   * ```
   *
   * then the field value is taken to *always* represent a formula, and the entire string is used
   * when passing the formula to {@link #config-handleFormulaChange}.
   *
   * If this `FormulaProvider` was inferred from the {@link #property-inputField}'s value starting with
   * a formula prefix (eg `=AI(`), then the string *between the parentheses* is used
   * and {@link #config-handleFormulaChange} is only called if the value matches eg `=AI(.*)`.
   *
   *  - `$fieldName` tokens replaced by the value of the named field from the configured {@link #property-record}
   *  - `$this` token replaced by a JSON representation of the string values of the {@link #property-record}
   *  - `$data` token replaced by a JSON representation of all records in the configured {@link #config-store}
   *
   * @prp {Core.widget.TextField}
   */
  inputField: {
    $config: "nullify",
    value: null
  },
  /**
   * A function or the name of a function in the ownership hierarchy to execute when the
   * {@link #property-inputField} is mutated by any means such as typing or pasting.
   * @prp {Function|String}
   * @param {Core.util.FormulaProvider} formulaProvider This FormulaProvider
   * @param {Core.widget.Field} inputField The input field being used
   * @returns {void}
   * @internal
   */
  handleFormulaInput: null,
  /**
   * A function or the name of a function in the ownership hierarchy to execute when
   * the input field fires its `change` event which is usually on blur.
   * @prp {Function|String}
   * @param {Core.util.FormulaProvider} formulaProvider This FormulaProvider
   * @param {String} formula The content of the typed formula with the following token substitutions made:
   *  - `$fieldName` tokens replaced by the value of the named field from the configured {@link #property-record}
   *  - `$this` token replaced by a JSON representation of the string values of the {@link #property-record}
   *  - `$data` token replaced by a JSON representation of all records in the configured {@link #config-store}
   * @param {Core.widget.Field} inputField The input field being used
   * @returns {void}
   * @internal
   */
  handleFormulaChange: null,
  /**
   * The URL to which the formula will be sent for evaluation in a JSON packet in
   * a property named by {@link #config-paramName}.
   *
   * The response is expected to be a JSON object from which the value of the
   * {@link #config-responseField} will be used as the result of the formula.
   *
   * ```json
   * {
   *   "content" : "Hello World!"
   * }
   * ```
   *
   * @prp {String}
   */
  url: null,
  /**
   * The HTTP method to use when sending the formula to the server.
   * @prp {String}
   * @default
   */
  method: "POST",
  /**
   * An object containing the Fetch options to pass to the server request. Use this to control if
   * credentials are sent and other options, read more at
   * [MDN](https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch).
   * @config {FetchOptions}
   */
  fetchOptions: null,
  /**
   * The name of the property in the JSON object sent to the server which contains the formula.
   *
   * It may be a dot-separated path to inject a nested value.
   * @prp {String}
   * @default
   */
  paramName: "prompt",
  /**
   * The name of the property in the JSON object received from the server which contains the result of the formula.
   *
   * It may be a dot-separated path to a nested value.
   *
   * Expected response format with the default value for this property ("content"):
   * ```json
   * {
   *   "content" : "Hello World!"
   * }
   * ```
   *
   * @prp {String}
   * @default
   */
  responseField: "content",
  /**
   * The headers to send with the request.
   * @prp {Object}
   * @default
   */
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json"
  },
  /**
   * A function or the name of a function in the ownership hierarchy which, when passed
   * a fully constructed formula, yields the result.
   *
   * Inject an implementation if the result is calculated programmatically or from a local
   * source instead of being generated by a remote server via the {@link #property-url}.
   * @prp {Function|String}
   * @param {String} formula The formula source
   * @returns {String} The result of the formula
   * @internal
   */
  generateContent: null,
  // Optional prefix if FormulaProvider instantiated.
  prefix: null
});
__publicField(_FormulaProvider, "factoryable", {
  defaultType: "formula"
});
var FormulaProvider = _FormulaProvider;
FormulaProvider.initClass();
FormulaProvider._$name = "FormulaProvider";

// ../Core/lib/Core/data/field/DataField.js
var { getOwnPropertyDescriptor: getOwnPropertyDescriptor2 } = Reflect;
var DataField = class extends Base.mixin(Factoryable_default) {
  /**
   * The class that first defined this field. Derived classes that override a field do not change this property.
   * @member {Core.data.Model} definedBy
   * @private
   * @readonly
   */
  /**
   * The class that most specifically defined this field. Derived classes that override a field set this property to
   * themselves.
   * @member {Core.data.Model} owner
   * @private
   * @readonly
   */
  // NOTE: Since we create lots of instances, they have no life cycle (they are not destroyed) and are readonly after
  // creation, this class only uses one configurable property.
  construct(config) {
    const me = this;
    if (config) {
      me.name = config.name;
      Object.assign(me, config);
    }
    if (me.compare) {
      me.compareItems = (itemA, itemB) => me.compare(itemA == null ? void 0 : itemA[me.name], itemB == null ? void 0 : itemB[me.name]);
    }
  }
  changeFormulaProviders(f) {
    const me = this;
    return new Proxy({ ...f }, {
      set(target, prop, value2) {
        const result = Reflect.set(target, prop, value2);
        me.updateFormulaProviders(target);
        return result;
      }
    });
  }
  // DataField gets this updated dynamically when Grid has a formulaProviders config.
  // GridBase injects the formulaProviders config into the dataField of each column.
  updateFormulaProviders(f) {
    this.inputPrefixRE = f ? new RegExp(`^=(${Object.keys(f).join("|")})\\(.*`) : null;
  }
  getFormulaProvider(prefix) {
    const provider = this.formulaProviders[prefix];
    if (provider) {
      if (provider.isFormulaProvider) {
        return provider;
      }
      let { instance } = provider;
      if (!instance) {
        const config = { ...provider, prefix };
        instance = provider.instance = FormulaProvider.get(prefix, config) || FormulaProvider.create(config);
      }
      return instance;
    }
  }
  get label() {
    return this._label || StringHelper.separate(this.name);
  }
  set label(label) {
    this._label = label;
  }
  /**
   * This method transforms a data value into the desired form for storage in the record's data object.
   *
   * ```javascript
   * export default class Task extends TaskModel {
   *    static get fields() {
   *        return [
   *            {
   *                name    : 'status',
   *                convert : (value, data) => {
   *                    if (value >= 100) {
   *                        return 'done';
   *                    }
   *                    else if (value > 0) {
   *                        return 'started';
   *                    }
   *                }
   *            }
   *        ];
   *    }
   * }
   * ```
   *
   * @method convert
   * @param {*} value The value to convert for storage in a record.
   * @param {Object} data The raw record data object
   * @returns {*} The converted value.
   */
  /**
   * This method transforms a data value into the desired form for transmitting to a server.
   * @method serialize
   * @param {*} value The value to serialize
   * @param {Core.data.Model} record The record that contains the value being serialized.
   * @returns {*} The serialized value.
   */
  /**
   * This optional method is called when setting a data value on a record.
   * @method set
   * @param {*} value The value to set
   * @param {Object} data The records future or current data object to set value to
   * @param {Core.data.Model} record The record that owns or will own the data object
   * @internal
   */
  /**
   * This optional method is called when a record using this field is created.
   * @method init
   * @param {Core.data.Model} record The record being created
   * @internal
   */
  /**
   * Create getter and setter functions for the specified field name under the specified key.
   * @internal
   */
  defineAccessor(target, force) {
    const { name, dataSource } = this;
    if (!force && name in target && target.$meta.hierarchy.some((current) => {
      var _a5;
      return ((_a5 = getOwnPropertyDescriptor2(current.prototype, name)) == null ? void 0 : _a5.enumerable) === false;
    })) {
      return;
    }
    if (this.formulaProviders) {
      target.$meta.class.addField(`#${name}`);
    }
    Reflect.defineProperty(target, name, {
      configurable: true,
      // To allow removing it later
      enumerable: true,
      // no arrow functions here, need `this` to change to instance
      get: this.complexMapping ? function() {
        return this.complexGet(name, dataSource);
      } : function() {
        if (this.batching && name in this.meta.batchChanges) {
          return this.meta.batchChanges[name];
        }
        return this.data[dataSource];
      },
      // no arrow functions here, need `this` to change to instance
      set(value2) {
        const field = this.$meta.fields.map[name];
        if (!(field && field.readOnly)) {
          this.set(name, value2);
        }
      }
    });
  }
  /**
   * Compares two values for this field and returns `true` if they are equal, and `false` if not.
   * @param {*} first The first value to compare for equality.
   * @param {*} second The second value to compare for equality.
   * @returns {Boolean} `true` if `first` and `second` are equal.
   */
  isEqual(first, second) {
    return ObjectHelper.isEqual(first, second);
  }
  /**
   * Returns the given field value as a `String`. If `value` is `null` or `undefined`, the value specified by
   * {@link #config-nullText} is returned.
   * @param {*} value The value to convert to a string.
   * @returns {String}
   */
  print(value2) {
    return value2 == null ? this.nullText : this.printValue(value2);
  }
  /**
   * Returns the given, non-null field value as a `String`.
   * @param {*} value The value to convert to a string (will not be `null` or `undefined`).
   * @returns {String}
   * @protected
   */
  printValue(value2) {
    return String(value2);
  }
  getCurrentConfig() {
    const result = {};
    for (const field in this.constructor.prototypeProperties) {
      result[field] = this[field];
    }
    delete result.isConstructing;
    return result;
  }
  get calculated() {
    return Boolean(this.calculate || this._calculated);
  }
  set calculated(value2) {
    this._calculated = value2;
  }
  get calculate() {
    return this._calculate;
  }
  set calculate(value2) {
    if (typeof value2 === "function") {
      value2 = { fn: value2 };
    }
    this._calculate = value2;
  }
};
__publicField(DataField, "$name", "DataField");
__publicField(DataField, "type", "auto");
__publicField(DataField, "factoryable", {
  defaultType: "auto"
});
__publicField(DataField, "prototypeProperties", {
  /**
   * The name of the field.
   *
   * If the name contains a dot (`.`), it is assumed to be a nested field, and the `complexMapping` config is
   * automatically set to `true`. This means that the field will point to nested data (path separated by dots in
   * the field name). Configure it as `false` to allow data field names that actually contain dots. See
   * {@link #config-complexMapping} for more information.
   *
   * @config {String} name
   */
  /**
   * The label text for a form item generated for this field. This is also used to create
   * a column header for a {@link #config-column} for this field.
   *
   * If no label is specified, the value yielded by this property is a prettified version of
   * the field name created using {@link Core.helper.StringHelper#function-separate-static}.
   * @config {String} label
   */
  /**
   * A column config object for a column to display this field in a grid. For simple, atomic
   * data types, such as `date`, `string`, `boolean`, `number` and `integer`, this is optional
   * and the appropriate column type can be inferred.
   *
   * This also provides default values for column configuration if a configured column definition
   * for a grid lacks a property.
   *
   * For complex fields, such as identifiers which link to other records, a more capable
   * column type may be specified, for example a `type : 'number'` field may be configured
   * with
   *
   * ```javascript
   * column : 'percent'
   * ```
   * or
   * ```javascript
   * column : {
   *     type : 'percent',
   *     width : 100
   * }
   * ```
   * if it represents a percentage value and needs appropriate rendering and editing.
   * @config {String|GridColumnConfig} column
   */
  /**
   * A config object for a widget to edit this field in a form. For simple, atomic
   * data types, such as `date`, `string`, `boolean`, `number` and `integer`, this is optional
   * and the appropriate input widget type can be inferred.
   *
   * For complex fields, such as identifiers which link to other records, a more capable
   * widget may be specified.
   * @config {String|InputFieldConfig} editor
   * @private
   */
  /**
   * A function that compares two values and returns a value < 0 if the first is less than the second, or 0
   * if the values are equal, or a value > 0 if the first is greater than the second.
   * @config {Function}
   * @param {Core.data.Model} value1
   * @param {Core.data.Model} value2
   * @returns {Number}
   * @default
   */
  compare: null,
  /**
   * A function that compares two objects or records using the `compare` function on the properties of each
   * object based on the `name` of this field.
   * @config {Function}
   * @param {Core.data.Model} value1
   * @param {Core.data.Model} value2
   * @returns {Number}
   * @default
   * @internal
   */
  compareItems: null,
  /**
   * The property in a record's data object that contains the field's value.
   * Defaults to the field's `name`.
   * @config {String}
   */
  dataSource: null,
  /**
   * The default value to assign to this field in a record if no value is provided.
   * @config {*} defaultValue
   */
  /**
   * Setting to `true` will ensure this field is included in any update/insert request payload
   * when a Store / Project / CrudManager performs a request.
   * @config {Boolean}
   * @default
   */
  alwaysWrite: false,
  /**
   * Setting to `false` indicates that `null` is not a valid value.
   * @config {Boolean}
   * @default
   */
  nullable: true,
  /**
   * The value to return from {@link #function-print} for a `null` or `undefined` value.
   * @config {String}
   * @default
   */
  nullText: null,
  /**
   * The value to replace `null` when the field is not `nullable`.
   * @config {*}
   * @default
   */
  nullValue: void 0,
  /**
   * Set to `false` to exclude this field when saving records to a server.
   * @config {Boolean}
   * @default
   */
  persist: true,
  /**
   * Set to `true` for the field's set accessor to ignore attempts to set this field.
   * @config {Boolean}
   * @default
   */
  readOnly: false,
  /**
   * By default, defined {@link Core.data.Model} fields may be used to create a grid column
   * suitable for displaying that field in a grid cell. Some fields may not be suitable for
   * features which automatically generate columns for view. These fields are created using
   * `internal : true`. Some examples are the `expanded` and `rowHeight` fields which are used
   * internally.
   * @config {Boolean}
   * @default
   */
  internal: false,
  /**
   * Set to `true` to indicate this field is calculated and cannot be edited via UI
   * @config {Boolean}
   * @internal
   * @default
   */
  calculated: false,
  /**
   * Lets you define a function to calculate the value of this field based on other record fields. The Model will
   * then try to auto-detect the dependencies used in the function. The calculate function can also return a
   * `Promise`, meaning you can fetch its value from a remote/async source.
   *
   * ```javascript
   * const store = new Store({
   *     fields : [
   *         {
   *              name : 'revenue',
   *              type : 'number'
   *         },
   *         {
   *              name : 'tax',
   *              type : 'number'
   *         },
   *         {
   *              name : 'net',
   *              calculate : record => record.revenue * (1 - (record.tax / 100)),
   *         }
   *     ],
   *     data : [
   *         { id : 1, revenue : 100, tax : 30 }
   *     ]
   * });
   *
   * const record = store.getById(1).net; // 70
   * ```
   * You can also pass an object with a `dependsOn` array of the dependent fields, along with the calculate `fn`:
   *
   * ```javascript
   * const store = new Store({
   *     fields : [
   *         {
   *              name : 'revenue',
   *              type : 'number'
   *         },
   *         {
   *              name : 'tax',
   *              type : 'number'
   *         },
   *         {
   *              name : 'net',
   *              calculate : {
   *                  fn        : record => record.revenue * (1 - (record.tax / 100)),
   *                  dependsOn : ['revenue', 'tax']
   *              }
   *         }
   *     ],
   *     data : [
   *         { id : 1, revenue : 100, tax : 30 }
   *     ]
   * });
   *
   * const record = store.getById(1).net; // 70
   * ```
   *
   * @config {Object|Function} calculate
   * @param {Function} calculate.fn The calculate function
   * @param {Core.data.Model} calculate.fn.record The record
   * @param {String[]} [calculate.dependsOn] An array of the other data fields this calculated field depends
   * on. If omitted, dependent fields are auto-detected and a `console.warn` debug messages will be issued
   * if it fails, and then you will need to declaratively add the dependent fields.
   * @returns {*|Promise} The calculated value or a Promise yielding the value
   */
  calculate: null,
  useProp: null,
  /**
   * When this flag is enabled, this field will skip the equality check when store is syncing the new
   * dataset (see {@link Core.data.Store#config-syncDataOnLoad} config). This means, that even if the
   * new value in new dataset is the same as old, it will still be applied to the model. It is useful
   * in certain edge case scenarios, when the update of the field does not preserve extra context information,
   * which should be provided by other fields.
   *
   * @config {Boolean}
   * @default
   */
  bypassEqualityOnSyncDataset: false,
  /**
   * Indicates that this field points to nested data (path separated by dots in the field name). Normally this is
   * set automatically when a field's {@link #config-name} or {@link #config-dataSource} contains a dot (`.`).
   * Configure it as `false` to allow data field names that actually contain dots.
   *
   * ```javascript
   * class MyModel extends Model {
   *    static fields = [
   *        // complexMapping automatically set to true,
   *        // `name.first` will point to { name : { first : "value" } }
   *        { name : 'name.first', type : 'string' },
   *
   *        // complexMapping automatically set to true,
   *        // `last` will point to { name : { last : "value" } }
   *        { name : 'last', type : 'string', dataSource : 'name.last' },
   *
   *        // complexMapping set to false,
   *        // `not.nested.name` will point to { "not.nested.name" : "value" }
   *        { name : 'not.nested.name', complexMapping : false }
   *    ];
   * }
   * ```
   *
   * @config {Boolean}
   */
  complexMapping: null
});
__publicField(DataField, "configurable", {
  /**
   * An object which names formula prefixes for use when editing this field.
   *
   * Each entry is keyed by a formula prefix, and each value is how to instantiate and configure a
   * {@link Core.util.FormulaProvider} when that prefix is used in the typed vale, eg: `=XXX(`
   *
   * If the configured value contains a `type` property, that is used to determine a registered
   * formula provider subclass to instantiate.
   *
   * ```javascript
   * fields : [{
   *     name             : 'review',
   *     formulaProviders : {
   *        AI : {
   *          type : 'remote',
   *          url  : 'https://my-ai-service.com'
   *        },
   *        SUM : {
   *           type : 'sum'
   *        }
   *     }
   * }]
   * ```
   *
   * Formula providers may be added to dynamically:
   *
   * ```javascript
   * // Enable registered MYFormula class to be used as a formula provider in the Grid
   * grid.store.modelClass.fieldMap.review.formulaProviders.MY = { ... };
   * ```
   * @config {Object<String,FormulaProviderConfig>} formulaProviders
   * @typings {Object<String,Object>} // This is used for nodeJS bundles, which should not have any refs to UI components
   */
  formulaProviders: null
});
DataField._$name = "DataField";

// ../Core/lib/Core/data/field/ArrayDataField.js
var ArrayDataField = class extends DataField {
  isEqual(a, b) {
    return a === b;
  }
  getAt(record, index) {
    return record.get(this.name)[index];
  }
};
__publicField(ArrayDataField, "$name", "ArrayDataField");
__publicField(ArrayDataField, "type", "array");
__publicField(ArrayDataField, "dataConstructor", Array);
ArrayDataField.initClass();
ArrayDataField._$name = "ArrayDataField";

// ../Core/lib/Core/data/field/BooleanDataField.js
var BooleanDataField = class extends DataField {
  static get prototypeProperties() {
    return {
      /**
       * The value to replace `null` when the field is not `nullable`. To automatically convert `null` or
       * `undefined` to `false`, set {@link #config-nullable} to `false`.
       * @config {Boolean}
       * @default
       */
      nullValue: false
    };
  }
  isEqual(first, second) {
    if (first == null && second == null) {
      return true;
    }
    return super.isEqual(first, second);
  }
  convert(value2) {
    var _a5;
    if (value2 == null) {
      return this.nullable ? value2 : this.nullValue;
    }
    if (((_a5 = value2.toLowerCase) == null ? void 0 : _a5.call(value2)) === "false") {
      return false;
    }
    return Boolean(value2);
  }
};
__publicField(BooleanDataField, "$name", "BooleanDataField");
__publicField(BooleanDataField, "type", "boolean");
__publicField(BooleanDataField, "dataConstructor", Boolean);
__publicField(BooleanDataField, "alias", "bool");
BooleanDataField.initClass();
BooleanDataField._$name = "BooleanDataField";

// ../Core/lib/Core/data/field/DateDataField.js
var DateDataField = class extends DataField {
  static get prototypeProperties() {
    return {
      /**
       * The format of the date field.
       *
       * See {@link Core.helper.DateHelper} for details.
       * @config {String} format
       * @default DateHelper.defaultFormat
       */
      format: null
    };
  }
  convert(value2) {
    if (value2 == null) {
      if (!this.nullable) {
        value2 = this.nullValue;
      }
    } else if (value2 === "now") {
      value2 = /* @__PURE__ */ new Date();
    } else if (!(value2 instanceof Date)) {
      const rawValue = value2;
      value2 = DateHelper.parse(value2, this.format || DateHelper.defaultParseFormat);
      if (!value2 || isNaN(value2)) {
        value2 = void 0;
        if (rawValue !== "") {
          console.warn(`Date parsing failed for "${this.name}" field, input: ${JSON.stringify(rawValue)}. ${this.format ? `Expected format: ${this.format}.` : `You should configure your Model field with a "format" to specify how dates should be parsed. See docs for DateDataField here: https://bryntum.com/products/scheduler/docs/api/Core/data/field/DateDataField, and see https://bryntum.com/products/scheduler/docs/api/Core/helper/DateHelper for the different formats.`}"`);
        }
      }
    }
    return value2;
  }
  serialize(value2) {
    if (value2 instanceof Date) {
      value2 = DateHelper.format(value2, this.format || DateHelper.defaultFormat);
    }
    return value2;
  }
  printValue(value2) {
    return DateHelper.format(value2, this.format || DateHelper.defaultFormat);
  }
};
__publicField(DateDataField, "$name", "DateDataField");
__publicField(DateDataField, "type", "date");
__publicField(DateDataField, "dataConstructor", Date);
DateDataField.initClass();
DateDataField._$name = "DateDataField";

// ../Core/lib/Core/data/field/StringDataField.js
var StringDataField = class extends DataField {
  static get prototypeProperties() {
    return {
      /**
       * The value to replace `null` when the field is not `nullable`.
       * @config {String}
       * @default
       */
      nullValue: ""
    };
  }
  convert(value2) {
    return value2 == null ? this.nullable ? value2 : this.nullValue : String(value2);
  }
};
__publicField(StringDataField, "$name", "StringDataField");
__publicField(StringDataField, "type", "string");
__publicField(StringDataField, "dataConstructor", String);
StringDataField.initClass();
StringDataField._$name = "StringDataField";

// ../Core/lib/Core/data/field/DurationUnitDataField.js
var DurationUnitDataField = class extends StringDataField {
  isEqual(first, second) {
    return DateHelper.compareUnits(first, second) === 0;
  }
};
__publicField(DurationUnitDataField, "$name", "DurationUnitDataField");
__publicField(DurationUnitDataField, "type", "durationunit");
DurationUnitDataField.initClass();
DurationUnitDataField._$name = "DurationUnitDataField";

// ../Core/lib/Core/data/field/IntegerDataField.js
var IntegerDataField = class extends DataField {
  static get prototypeProperties() {
    return {
      /**
       * The value to replace `null` when the field is not `nullable`.
       * @config {Number}
       * @default
       */
      nullValue: 0,
      /**
       * The `Math` method to use to ensure fractional component is removed.
       * @config {'round'|'floor'|'ceil'}
       * @default
       */
      rounding: "round"
    };
  }
  convert(value2) {
    return value2 == null ? this.nullable ? value2 : this.nullValue : Math[this.rounding](Number(value2));
  }
};
__publicField(IntegerDataField, "$name", "IntegerDataField");
__publicField(IntegerDataField, "type", "integer");
__publicField(IntegerDataField, "alias", "int");
__publicField(IntegerDataField, "dataConstructor", Number);
IntegerDataField.initClass();
IntegerDataField._$name = "IntegerDataField";

// ../Core/lib/Core/data/field/ModelDataField.js
var ModelDataField = class extends DataField {
  static get prototypeProperties() {
    return {
      complexMapping: true
    };
  }
  isEqual(first, second) {
    return first && second && second instanceof first.constructor && second.id == first.id;
  }
};
__publicField(ModelDataField, "$name", "ModelDataField");
__publicField(ModelDataField, "type", "model");
__publicField(ModelDataField, "configurable", {
  /**
   * Class used to contain data values in this field; should be a subclass of {@link Core.data.Model}.
   * Defining this configuration is necessary for some functionality (like filter editing) to identify the
   * type of data held by the field without data present.
   *
   * @config {Core.data.Model}
   * @typings {typeof Model}
   * @category Common
   */
  modelClass: null
});
ModelDataField.initClass();
ModelDataField._$name = "ModelDataField";

// ../Core/lib/Core/data/field/NumberDataField.js
var NumberDataField = class extends DataField {
  static get prototypeProperties() {
    return {
      /**
       * The value to replace `null` when the field is not `nullable`.
       * @config {Number}
       * @default
       */
      nullValue: 0,
      /**
       * The numeric precision of this field. Values are rounded to the specified number of digits. If `null`,
       * the default, no rounding is performed.
       * @config {Number}
       * @default
       */
      precision: null
    };
  }
  isEqual(first, second) {
    return isNaN(Number(first)) && isNaN(Number(second)) || super.isEqual(first, second);
  }
  convert(value2) {
    if (value2 == null) {
      return this.nullable ? value2 : this.nullValue;
    }
    value2 = Number(value2);
    if (isNaN(value2)) {
      return;
    }
    let scale = this.precision;
    if (scale) {
      scale = 10 ** scale;
      value2 = Math.round(value2 * scale) / scale;
    } else if (scale === 0) {
      value2 = Math.round(value2);
    }
    return value2;
  }
};
__publicField(NumberDataField, "$name", "NumberDataField");
__publicField(NumberDataField, "type", "number");
__publicField(NumberDataField, "alias", "float");
__publicField(NumberDataField, "dataConstructor", Number);
NumberDataField.initClass();
NumberDataField._$name = "NumberDataField";

// ../Core/lib/Core/data/field/ObjectDataField.js
var ObjectDataField = class extends DataField {
  static get prototypeProperties() {
    return {
      complexMapping: true
    };
  }
};
__publicField(ObjectDataField, "$name", "ObjectDataField");
__publicField(ObjectDataField, "type", "object");
__publicField(ObjectDataField, "dataConstructor", Object);
ObjectDataField.initClass();
ObjectDataField._$name = "ObjectDataField";

// ../Core/lib/Core/data/field/StoreDataField.js
var isIdAutoGenerated = (id2) => typeof id2 === "string" && id2.startsWith("_generated");
var StoreDataField = class extends DataField {
  /**
   * Store class to use when creating the store.
   *
   * ```javascript
   * class TodoStore extends Store {
   *     ...
   * }
   *
   * const task = new Store({
   *     static fields = [
   *         { type : 'store', name: 'todoItems', storeClass : TodoStore }
   *     ]
   * });
   * ```
   *
   * @config {Class} storeClass
   * @typings {typeof Store}
   */
  /**
   * Model class to use for the store (can also be configured as usual on the store class, this config is for
   * convenience).
   *
   * ```javascript
   * class TodoItem extends Model {
   *   ...
   * }
   *
   * const task = new Store({
   *     static fields = [
   *         { type : 'store', name: 'todoItems', storeClass : Store, modelClass : TodoItem }
   *     ]
   * });
   * ```
   *
   * @config {Class} modelClass
   * @typings {typeof Model}
   */
  /**
   * Optional store configuration object to apply when creating the store.
   *
   * ```javascript
   * const task = new Store({
   *     static fields = [
   *         {
   *             type       : 'store',
   *             name       : 'todoItems',
   *             storeClass : Store
   *             store      : {
   *                  syncDataOnLoad : false
   *             }
   *         }
   *     ]
   * });
   * ```
   *
   * @config {StoreConfig} store
   */
  // Initializer, called when creating a record. Sets up the store and populates it with any initial data
  init(data, record, chronoCallback) {
    var _a5;
    const me = this, storeName = `${me.name}Store`, config = {
      // Optimization when used from sources, don't create a stack in Base
      skipStack: true,
      syncDataOnLoad: true,
      // Incoming data is not expected to be polluted
      useRawData: false
    };
    if (me.store) {
      ObjectHelper.assign(config, me.store);
    }
    if (me.modelClass) {
      config.modelClass = me.modelClass;
    }
    (_a5 = record[`init${StringHelper.capitalize(storeName)}`]) == null ? void 0 : _a5.call(record, config);
    if (!config.storeClass && !me.storeClass) {
      throw new Error(`Field '${me.name}' with type 'store' must have a storeClass configured`);
    }
    const store = record.meta[storeName] = new (config.storeClass || me.storeClass)(config);
    if (me.complexMapping) {
      ObjectHelper.setPath(data, me.dataSource, store);
    } else {
      data[me.dataSource] = store;
    }
    store.verifyNoGeneratedIds = false;
    store.usesId = me.usesId || !store.count || !store.every((record2) => record2.hasGeneratedId);
    store.$currentValue = me.getValue(store, record);
    store.ion({
      change: ({ action }) => {
        const value2 = me.getValue(store, record);
        if (!store.$isSettingStoreFieldData) {
          const oldPreserveCurrentDataset = store.$preserveCurrentDataset;
          store.$preserveCurrentDataset = me.subStore && (action === "update" || action === "remove" || action === "add");
          me.$isUpdatingRecord = true;
          record.set(me.name, value2);
          me.$isUpdatingRecord = false;
          store.$preserveCurrentDataset = oldPreserveCurrentDataset;
        }
        store.$currentValue = value2;
      }
    });
    me.$chrono && chronoCallback(me, store);
  }
  // Called when setting a new value to the field on a record
  set(value2, data, record) {
    var _a5, _b2;
    const me = this, storeName = `${me.name}Store`, { [storeName]: store } = record.meta;
    if (value2 == null ? void 0 : value2.isStore) {
      value2 = value2.$currentValue;
    }
    if (!store) {
      record.meta.initableValues.set(me, value2);
      return false;
    }
    if (store.$isSettingStoreFieldData) {
      return;
    }
    store.$isSettingStoreFieldData = true;
    value2 = (_b2 = (_a5 = record[`process${StringHelper.capitalize(storeName)}Data`]) == null ? void 0 : _a5.call(record, value2, record)) != null ? _b2 : value2;
    if (!store.$preserveCurrentDataset) {
      store.data = value2;
    }
    store.$isSettingStoreFieldData = false;
    store.usesId = me.usesId || !store.count || !store.every((record2) => record2.hasGeneratedId);
  }
  serialize(value2, record) {
    const store = record.meta[`${this.name}Store`];
    return this.$isUpdatingRecord ? this.getValue(store, record) : store.$currentValue;
  }
  // Extract persistable values, optionally including id depending on if ids are used
  getValue(store, record) {
    const getValueFnName = `get${StringHelper.capitalize(this.name)}StoreValue`;
    if (record[getValueFnName]) {
      return record[getValueFnName](store);
    }
    return store.allRecords.map((r) => {
      const data = r.persistableData;
      if (!store.usesId) {
        delete data.id;
      }
      return data;
    });
  }
  isEqual(a, b) {
    if (a == null && b == null) {
      return true;
    } else if (a == null && b != null || a != null && b == null) {
      return false;
    } else {
      if (a.isStore && b.isStore) {
        return ObjectHelper.isDeeplyEqual(a.$currentValue, b.$currentValue);
      } else if (a.isStore && b.isStore || !a.isStore && !b.isStore) {
        return ObjectHelper.isEqual(a, b, true);
      } else {
        const store = a.isStore ? a : b, modelClass = store.modelClass, fields = modelClass.$meta.fields.defs, storeEntries = a.isStore ? a.$currentValue : b.$currentValue, arrayEntries = a.isStore ? b : a;
        if (storeEntries.length !== arrayEntries.length) {
          return false;
        } else {
          for (let i = 0; i < fields.length; i++) {
            const field = fields[i];
            if (!field.persist) {
              continue;
            }
            for (let k = 0; k < storeEntries.length; k++) {
              const storeValue = storeEntries[k][field.dataSource];
              const arrayValue = arrayEntries[k][field.dataSource];
              if (storeValue !== void 0 && arrayValue === void 0) {
                if (field.dataSource === modelClass.idField && isIdAutoGenerated(storeValue)) {
                  continue;
                }
                if (field.defaultValue !== void 0 && ObjectHelper.isEqual(field.defaultValue, storeValue, true)) {
                  continue;
                }
              }
              if (!ObjectHelper.isEqual(storeValue, arrayValue, true)) {
                return false;
              }
            }
          }
          return true;
        }
      }
    }
  }
  // Cloned value to be able to restore it later using STM
  getOldValue(record) {
    const store = record.meta[`${this.name}Store`];
    return store ? ObjectHelper.clone(store.$currentValue) : null;
  }
  getAt(record, index) {
    const store = record.meta[`${this.name}Store`];
    return store == null ? void 0 : store.getAt(index);
  }
};
__publicField(StoreDataField, "$name", "StoreDataField");
__publicField(StoreDataField, "type", "store");
StoreDataField.initClass();
StoreDataField._$name = "StoreDataField";

// ../Core/lib/Core/data/Model.js
var nestedRe = /^(.*?)\.(.*)/;
var arrayRe = /(.*)\[(.*)]\.?(.*)/;
var { defineProperty: defineProperty5 } = Reflect;
var { hasOwn: hasOwn4 } = ObjectHelper;
var _undefined = void 0;
var internalProps = {
  children: 1,
  data: 1,
  meta: 1
};
var abbreviationFields = [
  "name",
  "title",
  "text",
  "label",
  "description"
];
var fieldDataTypes = {
  boolean: 1,
  number: 1,
  date: 1,
  object: 1
};
var fieldsOrder = {
  parentId: 1,
  $PhantomId: 2,
  id: 3
};
var _Model = class _Model extends Base.mixin(ModelStm_default, TreeNode_default, ModelLink_default) {
  static get declarable() {
    return [
      /**
       * Array of defined fields for this model class. Subclasses add new fields by implementing this static
       * getter:
       *
       * ```javascript
       * // Model defining two fields
       * class Person extends Model {
       *     static get fields() {
       *         return [
       *             { name : 'username', defaultValue : 'New person' },
       *             { name : 'birthdate', type : 'date' }
       *         ];
       *     }
       * }
       *
       * // Subclass overriding one of the fields
       * class Bot extends Person {
       *     static get fields() {
       *         return [
       *             // Default value of 'username' field is overridden, any other setting from the parents
       *             // definition is preserved
       *             { name : 'username', defaultValue : 'Bot' }
       *         ];
       *     }
       * }
       * ```
       *
       * Fields in a subclass are merged with those from the parent class, making it easy to override mappings,
       * formats etc.
       *
       * Note that later accessing `Bot.fields` will refer to the block with the redefinition of the `username`
       * field seen above, not to all fields available (username, birthdate). To access all fields, use either
       * {@link #property-allFields-static} on the Model class, or the {@link #property-fields} property on a
       * record.
       *
       * @member {Array<String|ModelFieldConfig|Core.data.field.DataField>} fields
       * @readonly
       * @static
       * @category Fields
       */
      "fields"
    ];
  }
  static get fields() {
    return [
      // The index of this item in its parent (respects filtering)
      {
        name: "parentIndex",
        type: "number",
        persist: false,
        internal: true
      },
      // The index of this item in its parent ghost (non-sortable) children array
      {
        name: "orderedParentIndex",
        type: "number",
        persist: false,
        internal: true
      },
      /**
       * Flag the record as read-only on the UI level, preventing the end user from manipulating it using editing
       * features such as cell editing and event dragging.
       *
       * Does not prevent altering the record programmatically, it can still be manipulated by application code.
       *
       * For more info, see the "Read-only records" section above.
       *
       * @field {Boolean} readOnly
       * @category Common
       */
      {
        name: "readOnly",
        type: "boolean"
      },
      /**
       * Start expanded or not (only valid for tree data)
       * @readonly
       * @field {Boolean} expanded
       * @category Tree
       */
      {
        name: "expanded",
        internal: true
      }
    ];
  }
  /**
   * Template static getter which is supposed to be overridden to define default field values for the Model class.
   * Overrides `defaultValue` config specified by the {@link #property-fields-static} getter.
   * Returns a named object where key is a field name and value is a default value for the field.
   *
   * NOTE: This is a legacy way of defining default values, we recommend using {@link #property-fields-static} moving
   * forward.
   *
   * ```javascript
   * class Person extends Model {
   *     static get fields() {
   *         return [
   *             { name : 'username', defaultValue : 'New person' }
   *         ];
   *     }
   * }
   *
   * class Bot extends Person {
   *     static get defaults() {
   *         return {
   *             username : 'Bot' // default value of 'username' field is overridden
   *         };
   *     }
   * }
   * ```
   *
   * @member {Object} defaults
   * @static
   * @category Fields
   */
  /**
   * The data source for the id field which provides the ID of instances of this Model.
   * @property {String}
   * @category Fields
   */
  static set idField(idField) {
    this._assignedIdField = true;
    this._idField = idField;
  }
  static get idField() {
    return this._idField;
  }
  get id() {
    var _a5;
    return (_a5 = this._id) != null ? _a5 : this.getData("id");
  }
  set id(id2) {
    this.set("id", id2);
  }
  /**
   * The name of the data field which holds children of this Model when used in a tree structure
   * ```javascript
   * MyModel.childrenField = 'kids';
   * const parent = new MyModel({
   *     name : 'Dad',
   *     kids : [
   *         { name : 'Daughter' },
   *         { name : 'Son' }
   *     ]
   * });
   * ```
   * @property {String}
   * @category Fields
   */
  static set childrenField(childrenField) {
    this._childrenField = childrenField;
  }
  static get childrenField() {
    if (!this._childrenField) {
      const dataField = this.fieldMap.children;
      this._childrenField = (dataField == null ? void 0 : dataField.dataSource) || "children";
    }
    return this._childrenField;
  }
  /**
   * Returns index path to this node. This is the index of each node in the node path
   * starting from the topmost parent. (only relevant when its part of a tree store).
   * @returns {Number[]} The index of each node in the path from the topmost parent to this node.
   * @category Parent & children
   * @private
   */
  get indexPath() {
    const indices = [];
    let node = this, depth = node.childLevel;
    for (node = this; node && !node.isRoot; node = node.parent) {
      indices[depth--] = node.parentIndex + 1;
    }
    return indices;
  }
  /**
   * Unique identifier for the record. Might be mapped to another dataSource using idField, but always exposed as
   * record.id. Will get a generated value if none is specified in records data.
   *
   * {@note}Note that generated ids are meant to be temporary (phantom ids), they should not be serialized
   * but instead replaced by the backend on commit{/@note}
   *
   * @field {String|Number} id
   * @category Common
   */
  /**
   * Reports `true` when the record is a parent record generated by the TreeGroup feature.
   *
   * {@note}Only valid for stores used with {@link Grid.feature.TreeGroup} feature enabled.{/@note}
   * @member {Boolean} generatedParent Truthy flag for a generated parent record
   * @readonly
   * @category Identification
   */
  /**
   * Holds the value that a generated group parent represents when using the TreeGroup feature.
   *
   * {@note}Only valid for stores used with {@link Grid.feature.TreeGroup} feature.{/@note}
   * @member {*} key Gouping key identifier for a record
   * @readonly
   * @category Identification
   */
  //region Init
  /**
   * Constructs a new record from the supplied data config.
   * @param {Object} [config] Raw model config
   * @param {Core.data.Store} [store] Data store
   * @param {Object} [meta] Meta data
   * @privateparam {Boolean} [skipExpose] Skip exposing properties from data
   * @privateparam {Boolean} [forceUseRaw] Force using raw data, used by copy to not clone data twice
   * @function constructor
   * @category Lifecycle
   */
  construct(config = {}, store = null, meta = null, skipExpose = false, forceUseRaw = false, rawData = false) {
    var _a5, _b2;
    const me = this, { constructor, fieldMap } = me;
    me.meta = {
      modified: {},
      ...constructor.metaConfig,
      ...meta
    };
    if (constructor.applyConfigs) {
      const configs = {};
      if (!me.useRawData || !me.useRawData.enabled) {
        config = { ...config };
      }
      const defaultConfig = me.getDefaultConfiguration();
      if (constructor.autoExposeFields) {
        for (const key in defaultConfig) {
          if (key in config) {
            if (config[key] !== void 0) {
              configs[key] = config[key];
            }
            delete config[key];
          }
        }
      } else {
        for (const key in config) {
          if (!(key in constructor.fieldMap)) {
            if (config[key] === void 0) {
              configs[key] = defaultConfig[key];
            } else {
              configs[key] = config[key];
            }
            delete config[key];
          }
        }
      }
      super.construct(configs);
    }
    if (!skipExpose) {
      constructor.exposeProperties(config, rawData);
    }
    if (!hasOwn4(constructor, "idFieldProcessed")) {
      let overriddenIdField = me.meta.idField;
      if (!overriddenIdField) {
        if (constructor._assignedIdField) {
          overriddenIdField = constructor.idField;
        }
      }
      if (overriddenIdField && overriddenIdField !== fieldMap.id.dataSource) {
        constructor.addField({
          name: "id",
          dataSource: overriddenIdField,
          internal: true
        });
      }
      constructor._idField = fieldMap.id.dataSource;
      constructor.idFieldProcessed = true;
    }
    me._internalId = _Model._internalIdCounter++;
    if (!me.originalData) {
      me.originalData = config;
    }
    const data = constructor.processData(config, false, store, me, forceUseRaw);
    me._id = fieldMap.id.complexMapping ? ObjectHelper.getPath(data, constructor._idField) : data[constructor._idField];
    ((_a5 = me.meta.initableValues) == null ? void 0 : _a5.size) && me.assignInitables();
    if (me._id == null && !(constructor.allowNullId && me._id === null)) {
      me.setData("id", me.generateId(store));
    }
    if (data[constructor.childrenField]) {
      me.processChildren((_b2 = ArrayHelper.asArray(store)) != null ? _b2 : []);
    }
    me.generation = 0;
  }
  /**
   * Set this property to `true` when adding a record on a conditional basis, that is, it is yet
   * to be confirmed as an addition.
   *
   * When this is set, the {@link #property-isPersistable} value of the record is **false**, and upon being
   * added to a Store it will *not* be eligible to be synced with the server as an added record.
   *
   * Subsequently, *clearing* this property means this record will become persistable and eligible
   * for syncing as an added record.
   * @property {Boolean}
   * @category Editing
   */
  set isCreating(isCreating) {
    var _a5;
    const me = this;
    if (Boolean(me.meta.isCreating) !== isCreating) {
      me.meta.isCreating = isCreating;
      (_a5 = me.stores) == null ? void 0 : _a5.forEach((s) => s.onIsCreatingToggle(me, isCreating));
    }
  }
  get isCreating() {
    return Boolean(this.meta.isCreating);
  }
  /**
   * Compares this Model instance to the passed instance. If they are of the same type, and all fields
   * (except, obviously, `id`) are equal, this returns `true`.
   * @param {Core.data.Model} other The record to compare this record with.
   * @returns {Boolean} `true` if the other is of the same class and has all fields equal.
   * @category Misc
   */
  equals(other) {
    if (other instanceof this.constructor) {
      for (let fields = this.$meta.fields.defs, i = 0, { length } = fields; i < length; i++) {
        const field = fields[i], { name } = field;
        if (name !== "id" && !field.isEqual(this.getValue(name), other.getValue(name))) {
          return false;
        }
      }
      return true;
    }
    return false;
  }
  // get subclass() {
  //     return new this.constructor(Object.setPrototypeOf({
  //         id : _undefined
  //     }, this.data), this.stores[0], null, true);
  // }
  /**
   * Processes raw data, converting values and setting defaults.
   * @private
   * @param {Object} data Raw data
   * @param {Boolean} [updating] Ignore setting default values, used when updating
   * @returns {Object} Processed data
   * @category Fields
   */
  static processData(data, updating = false, store, record, forceUseRaw) {
    const me = this, {
      map: fieldMap,
      defaults,
      chronoProps
    } = me.$meta.fields, { useRawData = { enabled: false } } = store || {}, processed = forceUseRaw || useRawData.enabled ? data : ObjectHelper.clone(data), isChrono = Boolean(record.$entity);
    if (record.isConstructing) {
      record.data = processed;
    }
    let fieldName, generate$;
    if (isChrono && !updating) {
      generate$ = {};
      for (const prop of chronoProps) {
        generate$[prop.name] = record.createFieldIdentifier(prop);
      }
      Object.defineProperty(record, "$", { value: generate$, enumerable: true, configurable: true });
    }
    const ignoreDefaults = updating || useRawData.disableDefaultValue || forceUseRaw;
    if (!ignoreDefaults || !useRawData.disableTypeConversion && !forceUseRaw || isChrono) {
      for (fieldName in fieldMap) {
        const field = fieldMap[fieldName], { name, dataSource } = field, hasSource = dataSource !== name, complex = field.complexMapping, sourceValue = complex ? ObjectHelper.getPath(processed, dataSource) : processed[dataSource], nameValue = sourceValue === void 0 ? complex ? ObjectHelper.getPath(processed, name) : processed[name] : void 0, useSourceForValue = sourceValue !== void 0, hasNameValue = nameValue !== void 0, useNameForValue = !useSourceForValue && hasNameValue, hasValue = useSourceForValue || hasNameValue, convert = !useRawData.disableTypeConversion && field.convert;
        let value2 = useNameForValue ? nameValue : sourceValue;
        if (!ignoreDefaults && dataSource in defaults && !hasValue && !field.fromData) {
          let defaultValue2 = defaults[dataSource];
          if (Array.isArray(defaultValue2)) {
            defaultValue2 = defaultValue2.slice();
          } else if (defaultValue2 instanceof Date) {
            defaultValue2 = new Date(defaultValue2);
          } else if (defaultValue2 && typeof defaultValue2 === "object") {
            defaultValue2 = ObjectHelper.clone(defaultValue2);
          }
          if (complex) {
            ObjectHelper.setPath(processed, dataSource, defaultValue2);
          } else {
            processed[dataSource] = defaultValue2;
          }
          value2 = defaultValue2;
        }
        if ((useNameForValue || convert) && (!ignoreDefaults || hasValue) && !forceUseRaw && !field.calculate) {
          let converted = convert ? field.convert(value2, data, record) : value2;
          if (me.processFieldData) {
            converted = me.processFieldData(field, converted, value2, data, record);
          }
          if (!(converted === void 0 && value2 === void 0 && !useSourceForValue)) {
            if (complex) {
              ObjectHelper.setPath(processed, dataSource, converted);
            } else {
              processed[dataSource] = converted;
            }
            if (hasSource) {
              delete processed[name];
            }
            value2 = converted;
          }
        }
        if (!updating && field.$chrono) {
          const identifier = generate$[name] = record.createFieldIdentifier(field.$chrono);
          identifier.DATA = value2;
        }
      }
    }
    me.$meta.fields.calculated.forEach((field) => {
      const { calculate } = field;
      if (!field._cachedDependencies && !calculate.dependsOn) {
        field._cachedDependencies = true;
        const names2 = /* @__PURE__ */ new Set(), proxy = new Proxy(processed, {
          get(obj, name) {
            names2.add(name);
            return processed[name];
          }
        });
        let autoDetectFailed;
        try {
          calculate.fn(proxy, record);
          calculate.dependsOn = Array.from(names2);
          me.setupDependentCalcFields(field);
          autoDetectFailed = names2.size === 0;
        } catch (e) {
          autoDetectFailed = true;
        }
        if (autoDetectFailed) {
          console.warn(`Failed to auto-detect dependent fields for "${field.name}" field. Add "dependentOn" in your calculate config.`);
        }
      }
      processed[field.name] = record.refreshCalculatedField(field);
    });
    me.$meta.fields.initable.length && me.initInitables(record, processed, (field, value2) => {
      const identifier = generate$[field.name] = record.createFieldIdentifier(field.$chrono);
      identifier.DATA = value2;
    });
    return processed;
  }
  static setupClass(meta) {
    super.setupClass(meta);
    if (!meta.fields) {
      this.setupFields(this, meta);
    }
  }
  static setupFields(cls, meta) {
    var _a5, _b2, _c2, _d, _e;
    const classFields = hasOwn4(cls, "fields") && cls.fields, base = meta.super.fields, fieldsInfo = meta.fields = {
      defs: (_a5 = base == null ? void 0 : base.defs.slice()) != null ? _a5 : [],
      calculated: (_b2 = base == null ? void 0 : base.calculated.slice()) != null ? _b2 : [],
      // Set to `true` when an instance's data object is run through exposeProperties
      exposedData: false,
      // These objects are all keyed by field name:
      defaults: base ? { ...base.defaults } : {},
      // value=field.defaultValue
      exposed: Object.create((_c2 = base == null ? void 0 : base.exposed) != null ? _c2 : null),
      // value=true if we've done defineProperty
      ordinals: Object.create((_d = base == null ? void 0 : base.ordinals) != null ? _d : null),
      // value=index in the defs array
      map: base ? { ...base.map } : {},
      // value=definition object
      sources: base ? { ...base.sources } : {},
      // value=source definition object
      chronoProps: [],
      // engine props that are not fields
      subModels: []
    };
    if (cls !== _Model) {
      let parent = meta;
      do {
        parent = parent.super;
        parent.fields.subModels.push(cls);
      } while (parent.class !== _Model);
    }
    if (hasOwn4(cls, "defaults")) {
      Object.assign(fieldsInfo.defaults, cls.defaults);
    }
    if (hasOwn4(cls, "idField")) {
      cls.addField({
        name: "id",
        dataSource: cls.idField,
        internal: true
      });
      fieldsInfo.exposed[cls.idField] = true;
    }
    if (classFields == null ? void 0 : classFields.length) {
      classFields.map(cls.addField, cls);
    }
    fieldsInfo.initable = fieldsInfo.defs.filter((field) => field.init);
    if ((_e = cls.$entity) == null ? void 0 : _e.allFields.size) {
      for (const chronoField of cls.$entity.allFields.values()) {
        if (!fieldsInfo.map[chronoField.name]) {
          fieldsInfo.chronoProps.push(chronoField);
        }
      }
    }
    cls.exposeRelations();
  }
  static get defaultValues() {
    return this.$meta.fields.defaults;
  }
  /**
   * An array containing all the _defined_ fields for this Model class. This will include all superclass's
   * defined fields.
   * @property {Core.data.field.DataField[]}
   * @static
   * @readonly
   * @category Fields
   */
  static get allFields() {
    return this.$meta.fields.defs;
  }
  /**
   * Same as {@link #property-allFields-static}.
   * @property {Core.data.field.DataField[]}
   * @readonly
   * @category Fields
   */
  get allFields() {
    return this.$meta.fields.defs;
  }
  /**
   * An object containing all the _defined_ fields for this Model class. This will include all superclass's
   * defined fields through its prototype chain. So be aware that `Object.keys` and `Object.entries` will only
   * access this class's defined fields.
   * @property {Object<String,Core.data.field.DataField>}
   * @static
   * @readonly
   * @category Fields
   */
  static get fieldMap() {
    return this.$meta.fields.map;
  }
  /**
   * Same as {@link #property-fieldMap-static}.
   * @property {Object<String,Core.data.field.DataField>}
   * @readonly
   * @category Fields
   */
  get fieldMap() {
    return this.$meta.fields.map;
  }
  static get fieldDataSourceMap() {
    return this.$meta.fields.sources;
  }
  /**
   * Makes getters and setters for fields (from definitions and data). Called once when class is defined and once when
   * data is loaded first time.
   * @internal
   * @param {Object} [data] Raw data
   * @param {Boolean} [raw=true] True if data is raw (contains data sources), False if data contains field names
   * @category Fields
   */
  static exposeProperties(data, raw = true) {
    const me = this, fieldsInfo = me.$meta.fields, fieldMapProperty = raw ? "exposed" : "map";
    if (data && me.autoExposeFields && !fieldsInfo.exposedData) {
      let dataProperty, fieldDef, type;
      for (dataProperty in data) {
        if (!fieldsInfo[fieldMapProperty][dataProperty] && dataProperty !== me.childrenField) {
          type = ObjectHelper.typeOf(data[dataProperty]);
          fieldDef = {
            name: dataProperty,
            dataSource: dataProperty,
            fromData: true
          };
          if (fieldDataTypes[type]) {
            fieldDef.type = type;
          }
          me.addField(fieldDef);
        }
      }
      fieldsInfo.exposedData = true;
    }
    me.exposeRelations();
  }
  /**
   * Add a field definition in addition to those predefined in `fields`.
   * @param {String|ModelFieldConfig} fieldDef A field name or definition
   * @returns {Core.data.field.DataField}
   * @category Fields
   */
  static addField(fieldDef) {
    var _a5;
    if (fieldDef == null) {
      return;
    }
    if (typeof fieldDef === "string") {
      fieldDef = {
        name: fieldDef
      };
    }
    const me = this.initClass(), fieldsInfo = me.$meta.fields, {
      ordinals,
      calculated,
      subModels
    } = fieldsInfo, propertiesExposed = fieldsInfo.exposed, { name } = fieldDef, existing = fieldsInfo.map[name], dataSource = fieldDef.dataSource || (fieldDef.dataSource = name);
    let field, key;
    if (!existing || fieldDef.type && fieldDef.type !== existing.type) {
      fieldDef.model = me;
      field = DataField.create(fieldDef);
      fieldDef.model = null;
      field.definedBy = existing ? existing.definedBy : me;
      field.ordinal = existing ? existing.ordinal : ordinals[name] = fieldsInfo.defs.length;
      if ((_a5 = field.calculate) == null ? void 0 : _a5.dependsOn) {
        me.setupDependentCalcFields(field);
      }
    } else {
      field = Object.create(existing);
      for (key in fieldDef) {
        if (key !== "type") {
          field[key] = fieldDef[key];
        }
      }
    }
    field.owner = me;
    fieldsInfo.defs[field.ordinal] = field;
    fieldsInfo.map[name] = field;
    fieldsInfo.sources[dataSource] = field;
    for (const subModel of subModels) {
      subModel.$meta.fields.map[name] = field;
    }
    if (field.complexMapping !== false && dataSource.includes(".")) {
      field.complexMapping = true;
    }
    if (field.complexMapping) {
      propertiesExposed[dataSource.split(".")[0]] = true;
    } else {
      propertiesExposed[dataSource] = true;
    }
    if ("defaultValue" in field) {
      fieldsInfo.defaults[dataSource] = field.defaultValue;
    }
    if (!internalProps[name]) {
      field.defineAccessor(me.prototype);
    }
    if (field.calculate) {
      calculated.push(field);
    }
    me._nonPersistableFields = null;
    me._alwaysWriteFields = null;
    return field;
  }
  static setupDependentCalcFields(field) {
    const fieldsInfo = this.$meta.fields;
    field.calculate.dependsOn.forEach((depName) => {
      if (!fieldsInfo.map[depName]) {
        throw new Error(`No "${depName}" field defined in ${this.$name} class`);
      }
      fieldsInfo.map[depName].dependentFields = fieldsInfo.map[depName].dependentFields || [];
      fieldsInfo.map[depName].dependentFields.push(field.name);
    });
  }
  /**
   * Remove a field definition by name.
   * @param {String} fieldName Field name
   * @category Fields
   */
  static removeField(fieldName) {
    const me = this.initClass(), fieldsInfo = me.$meta.fields, definition = fieldsInfo.map[fieldName], { ordinals } = fieldsInfo, index = ordinals[fieldName];
    if (definition) {
      fieldsInfo.defs.splice(index, 1);
      delete ordinals[fieldName];
      delete fieldsInfo.defaults[fieldName];
      delete fieldsInfo.exposed[fieldName];
      delete fieldsInfo.map[fieldName];
      delete fieldsInfo.sources[definition.dataSource];
      for (const name in ordinals) {
        if (ordinals[name] > index) {
          --ordinals[name];
        }
      }
      delete me.prototype[fieldName];
    }
  }
  /**
   * Makes getters and setters for related records. Populates a Model#relation array with the relations, to allow it
   * to be modified later when assigning stores.
   * @internal
   * @category Relations
   */
  static exposeRelations() {
    const me = this;
    if (hasOwn4(me, "exposedRelations")) {
      return;
    }
    if (me.relations) {
      me.exposedRelations = [];
      for (const relationName in me.relations) {
        const relation = me.relations[relationName];
        relation.relationName = relationName;
        me.exposedRelations.push(relation);
        if (!Reflect.ownKeys(me.prototype).includes(relationName)) {
          defineProperty5(me.prototype, relationName, {
            enumerable: true,
            get: function() {
              return this.getForeign(relationName);
            },
            set: function(value2) {
              this.setForeign(relationName, value2, relation);
            }
          });
        }
      }
    }
  }
  //endregion
  //region Initable fields
  // Initializes any fields using a data type that has an init method, and caches the value to assign to that field to
  // be able to assign it after all others. That allows the initter to reference the records other data if needed
  // (baselines use that to reference the task to get default values)
  static initInitables(record, processedData, chronoCallback) {
    const laterValues = record.meta.initableValues = /* @__PURE__ */ new Map();
    for (const field of this.$meta.fields.initable) {
      const value2 = ObjectHelper.getPath(processedData, field.dataSource);
      value2 !== void 0 && laterValues.set(field, value2);
      !field.lazy && field.init(processedData, record, chronoCallback);
    }
  }
  // Assigns values to the fields that were initialized earlier (see initInitables above)
  assignInitables() {
    const { initableValues } = this.meta;
    for (const [field, value2] of initableValues) {
      if (field.set(value2, this.data, this) !== false) {
        initableValues.delete(field);
      }
    }
  }
  //endregion
  //region Fields
  /**
   * Flag checked from Store when loading data that determines if fields found in first records should be exposed in
   * same way as predefined fields.
   *
   * {@note}Note that we for all but the most basic use cases recommend explicitly defining the fields.
   * Having them auto exposed can lead to unexpected behavior, if the first record is not complete (fields missing,
   * null etc).
   * {/@note}
   *
   * @property {Boolean}
   * @category Fields
   */
  static get autoExposeFields() {
    return true;
  }
  /**
   * This function forces correct field order. Correct order is parentId before id. If we process id field before
   * parentId, idMap won't be updated and changing parent node will lead to duplicated records in storage
   * @param {String} a
   * @param {String} b
   * @returns {number}
   * @private
   */
  static fieldSorter(a, b) {
    return (fieldsOrder[a] || 100) - (fieldsOrder[b] || 100);
  }
  /**
   * Convenience getter to get field definitions from class.
   * @property {Core.data.field.DataField[]}
   * @readonly
   * @category Fields
   */
  get fields() {
    return this.$meta.fields.defs;
  }
  /**
   * Convenience function to get the definition for a field from class.
   * @param {String} fieldName Field name
   * @returns {Core.data.field.DataField}
   * @category Fields
   */
  getFieldDefinition(fieldName) {
    return this.constructor.getFieldDefinition(fieldName);
  }
  getFieldDefinitionFromDataSource(dataSource) {
    return this.$meta.fields.sources[dataSource];
  }
  /**
   * Get the names of all properties in the data object.
   *
   * Note that this is not the same as the fields defined for the model, in most cases you probably want to use
   * {@link #property-fields} instead.
   *
   * @property {String[]}
   * @readonly
   * @category Fields
   */
  get fieldNames() {
    return Object.keys(this.data);
  }
  /**
   * Get the definition for a field by name.
   * @param {String} fieldName Field name
   * @returns {Core.data.field.DataField} Field definition or null if none found
   * @category Fields
   */
  static getFieldDefinition(fieldName) {
    var _a5;
    const field = this.fieldMap[fieldName];
    if (!field) {
      if (fieldName.includes("[")) {
        const [, arrayFieldName, , path] = fieldName.match(arrayRe), arrayField = arrayFieldName.includes(".") ? this.getFieldDefinition(arrayFieldName) : this.fieldMap[arrayFieldName];
        if (arrayField == null ? void 0 : arrayField.isStoreDataField) {
          return (_a5 = arrayField.modelClass) == null ? void 0 : _a5.getFieldDefinition(path);
        }
      } else if (fieldName.includes(".")) {
        const [localFieldName, path] = fieldName.split(".", 2), modelField = this.fieldMap[localFieldName];
        if ((modelField == null ? void 0 : modelField.isModelDataField) && modelField.modelClass) {
          return modelField.modelClass.getFieldDefinition(path);
        }
      }
    }
    return field;
  }
  /**
   * Returns dataSource configuration for a given field name
   * @param {String} fieldName
   * @returns {String} Field `dataSource` mapping
   * @internal
   */
  static getFieldDataSource(fieldName) {
    var _a5;
    return ((_a5 = this.getFieldDefinition(fieldName)) == null ? void 0 : _a5.dataSource) || fieldName;
  }
  /**
   * Get the data source used by specified field. Returns the fieldName if no data source specified.
   * @param {String} fieldName Field name
   * @returns {String}
   * @category Fields
   */
  getDataSource(fieldName) {
    const def = this.constructor.getFieldDefinition(fieldName);
    return (def == null ? void 0 : def.dataSource) || (def == null ? void 0 : def.name);
  }
  /**
   * Processes input to a field, converting to expected type.
   * @param {String} fieldName Field name
   * @param {*} value Value to process
   * @returns {*} Converted value
   * @category Fields
   */
  static processField(fieldName, value2, record) {
    const field = this.fieldMap[fieldName];
    return (field == null ? void 0 : field.convert) ? field.convert(value2, this.data, record) : value2;
  }
  //endregion
  //region Relations
  /**
   * Initializes model relations. Called from store when adding a record.
   * @private
   * @category Relations
   */
  initRelations() {
    const me = this, relations = me.constructor.exposedRelations;
    if (!relations) {
      return;
    }
    me.stores.forEach((store) => {
      var _a5;
      if (!store.modelRelations) {
        store.initRelations();
      }
      const relatedRecords = [];
      (_a5 = store.modelRelations) == null ? void 0 : _a5.forEach((config) => {
        relatedRecords.push({ related: me.initRelation(config), config });
      });
      store.updateRecordRelationCache(me, relatedRecords);
    });
  }
  /**
   * Initializes/updates a single relation.
   * @param config Relation config
   * @returns {Core.data.Model} Related record
   * @private
   * @category Relations
   */
  initRelation(config) {
    const me = this, foreignId = me.get(config.foreignKey), foreign = foreignId !== _undefined && typeof config.foreignStore !== "string" && config.foreignStore.getById(foreignId), relationCache = me.meta.relationCache || (me.meta.relationCache = {});
    relationCache[config.relationName] = foreign || (foreignId != null ? { id: foreignId, placeHolder: true } : null);
    return foreign;
  }
  removeRelation(config) {
    const { relationName, foreignKey, nullFieldOnRemove } = config;
    if (this.meta.relationCache[relationName]) {
      delete this.meta.relationCache[relationName];
      if (nullFieldOnRemove) {
        this.setData(foreignKey, null);
      }
    }
  }
  getForeign(name) {
    var _a5;
    return (_a5 = this.meta.relationCache) == null ? void 0 : _a5[name];
  }
  setForeign(name, value2, config) {
    const id2 = _Model.asId(value2);
    return this.set(config.foreignKey, id2);
  }
  /**
   * Get a relation config by name, from the first store.
   * @param {String} name
   * @returns {Object}
   * @private
   * @category Relations
   */
  getRelationConfig(name) {
    var _a5, _b2;
    return (_b2 = (_a5 = this.firstStore) == null ? void 0 : _a5.modelRelations) == null ? void 0 : _b2.find((r) => r.foreignKey === name);
  }
  //endregion
  //region Get/set values, data handling
  flatGet(fieldName, dataSource) {
    if (this.batching && fieldName in this.meta.batchChanges) {
      return this.meta.batchChanges[fieldName];
    }
    return this.data[dataSource];
  }
  complexGet(fieldName, dataSource) {
    if (this.batching && fieldName in this.meta.batchChanges) {
      return this.meta.batchChanges[fieldName];
    }
    return ObjectHelper.getPath(this.data, dataSource);
  }
  /**
   * Get value for specified field name. You can also use the generated getters if loading through a Store.
   * If model is currently in batch operation this will return updated batch values which are not applied to Model
   * until endBatch() is called.
   * @param {String} fieldName Field name to get value from
   * @returns {*} Fields value
   * @category Fields
   */
  get(fieldName) {
    if (!fieldName) {
      return;
    }
    const me = this, { fieldMap } = me, field = fieldMap[fieldName];
    if (!field) {
      if (fieldName.includes("[")) {
        const [, arrayFieldName, index, path] = fieldName.match(arrayRe);
        const arrayField = fieldMap[arrayFieldName];
        if (arrayField == null ? void 0 : arrayField.getAt) {
          me._thisIsAUsedExpression(me[arrayFieldName]);
          const subRecord = arrayField.getAt(me, index);
          if (subRecord && path) {
            if (subRecord.isModel) {
              return subRecord.getValue(path);
            }
            return subRecord[path];
          }
          return subRecord;
        }
        return null;
      }
      if (fieldName.includes(".")) {
        const [localFieldName, nestedFieldName] = fieldName.split(".", 2), localField = fieldMap[localFieldName];
        if (localField == null ? void 0 : localField.isStoreDataField) {
          const store = me[localFieldName], storeModelClass = store.modelClass;
          if (nestedFieldName in storeModelClass.fieldMap || !(nestedFieldName in store)) {
            return store == null ? void 0 : store.getDistinctValues(nestedFieldName);
          }
          return store[nestedFieldName];
        } else if (!ObjectHelper.hasPath(me.data, fieldName)) {
          return ObjectHelper.getPath(me, fieldName);
        }
        return me.complexGet(fieldName, fieldName);
      }
    }
    if (field == null ? void 0 : field.complexMapping) {
      return me.complexGet(fieldName, field.dataSource);
    }
    return me.flatGet(fieldName, (field == null ? void 0 : field.dataSource) || fieldName);
  }
  // Used to get field values, replaces `record[fieldName]` in internal code to allow relations etc.
  getValue(fieldName) {
    if (!fieldName) {
      return;
    }
    if (fieldName in this && !fieldName.includes(".")) {
      return this[fieldName];
    }
    return this.get(fieldName);
  }
  // Used to set field values, replacing `record[fieldName] = value` to handle dot notation
  setValue(fieldName, value2) {
    if (fieldName in this) {
      this[fieldName] = value2;
    } else {
      this.set(fieldName, value2);
    }
  }
  /**
   * Internal function used to update a records underlying data block (record.data) while still respecting field
   * mappings. Needed in cases where a field needs setting without triggering any associated behaviour and it has a
   * dataSource with a different name.
   *
   * For example:
   * ```javascript
   * // startDate mapped to data.beginDate
   * { name : 'startDate', dataSource : 'beginDate' }
   *
   * // Some parts of our code needs to update the data block without triggering any of the behaviour associated with
   * // calling set. This would then not update "beginDate":
   * record.data.startDate = xx;
   *
   * // But this would
   * record.setData('startDate', xx);
   * ```
   * @internal
   * @category Editing
   */
  setData(toSet, value2) {
    var _a5, _b2;
    const { data, fieldMap } = this;
    if (typeof toSet === "string") {
      const field = fieldMap[toSet], dataSource = (_a5 = field == null ? void 0 : field.dataSource) != null ? _a5 : toSet;
      if (field == null ? void 0 : field.set) {
        field.set(value2, this.data, this);
      } else if (field == null ? void 0 : field.complexMapping) {
        ObjectHelper.setPath(data, dataSource, value2);
      } else {
        data[dataSource] = value2;
      }
      if (field === "id") {
        this._id = value2;
      } else if (toSet === "parentId") {
        data[this.constructor.parentIdField] = value2;
      }
    } else {
      const keys = Object.keys(toSet);
      for (let i = 0; i < keys.length; i++) {
        const fieldName = keys[i], field = fieldMap[fieldName], dataSource = (_b2 = field == null ? void 0 : field.dataSource) != null ? _b2 : fieldName;
        if (field == null ? void 0 : field.set) {
          field.set(value2, this.data, this);
        } else if (dataSource) {
          if (field == null ? void 0 : field.complexMapping) {
            ObjectHelper.setPath(data, dataSource, toSet[fieldName]);
          } else {
            data[dataSource] = toSet[fieldName];
          }
          if (fieldName === "id") {
            this._id = value2;
          } else if (fieldName === "parentId") {
            data[this.constructor.parentIdField] = value2;
          }
        }
      }
    }
  }
  /**
   * Returns raw data from the encapsulated data object for the passed field name
   * @param {String} fieldName The field to get data for.
   * @returns {*} The raw data value for the field.
   * @category Editing
   */
  getData(fieldName) {
    var _a5;
    const field = this.fieldMap[fieldName], dataSource = (_a5 = field == null ? void 0 : field.dataSource) != null ? _a5 : fieldName;
    if (dataSource) {
      if (field == null ? void 0 : field.complexMapping) {
        return ObjectHelper.getPath(this.data, dataSource);
      }
      return this.data[dataSource];
    }
  }
  /**
   * Silently updates record's id with no flagging the property as modified.
   * Triggers onModelChange event for changed id.
   * @param {String|Number} value id value
   * @private
   */
  syncId(value2) {
    const oldValue = this.id;
    if (oldValue !== value2) {
      this._id = value2;
      this.setData("id", value2);
      const data = { id: { value: value2, oldValue } };
      this.afterChange(data, data);
    }
  }
  /**
   * Set value for the specified field. You can also use the generated setters if loading through a Store.
   *
   * Setting a single field, supplying name and value:
   *
   * ```javascript
   * record.set('name', 'Clark');
   * ```
   *
   * Setting multiple fields, supplying an object:
   *
   * ```javascript
   * record.set({
   *     name : 'Clark',
   *     city : 'Metropolis'
   * });
   * ```
   *
   * NOTE: Do not modify fields using `dataSource`, as it is intended only for reading and writing from the raw data object.
   * Fields should be modified using `name` as it is the public interface.
   *
   * @param {String|Object} field The field to set value for, or an object with multiple values to set in one call
   * @param {*} [value] Value to set
   * @param {Boolean} [silent] Set to `true` to not trigger events. If event is recurring, occurrences won't be updated automatically.
   * @privateparam {Boolean} [validOnly] If set to `true` it will ignore setting a `undefined` value, allowing conversion functions to invalidate a data input
   * @privateparam {Boolean} [triggerBeforeUpdate]
   * @fires Core.data.Store#event-idChange
   * @fires Core.data.Store#event-update
   * @fires Core.data.Store#event-change
   * @category Editing
   */
  set(field, value2, silent = false, fromRelationUpdate = false, skipAccessors = false, validOnly = false, triggerBeforeUpdate = true) {
    var _a5;
    const me = this;
    if (me.isBatchUpdating) {
      me.inBatchSet(field, value2, silent || me.$silenceBatch);
    } else {
      const wasSet = me.inSet(field, value2, silent, fromRelationUpdate, skipAccessors, validOnly, triggerBeforeUpdate);
      (_a5 = me.afterSet) == null ? void 0 : _a5.call(me, field, value2, silent, fromRelationUpdate, wasSet);
      return wasSet;
    }
  }
  updateDependentFieldValues(field) {
    if (typeof field === "object") {
      Object.keys(field).forEach((key) => this.updateDependentFieldValues(key));
    } else {
      if (field.startsWith("#")) {
        field = field.substring(0);
      }
      for (const [key, value2] of Object.entries(this.constructor.dependentFields || {})) {
        if (field in value2) {
          this.set(key, this.get(`#${key}`));
        }
      }
    }
  }
  fieldToKeys(field, value2) {
    if (typeof field !== "string") {
      return ObjectHelper.assign({}, field);
    }
    return {
      [field]: value2
    };
  }
  inBatchSet(field, value2, silent) {
    var _a5;
    const me = this, {
      meta,
      constructor,
      fieldMap
    } = me, wasSet = {};
    let cmp, changed = false;
    if (typeof field !== "string") {
      Object.keys(me.fieldToKeys(field, value2)).forEach((key) => {
        cmp = fieldMap[key] || ObjectHelper;
        value2 = constructor.processField(key, field[key], me);
        if (!cmp.isEqual(meta.batchChanges[key], value2)) {
          wasSet[key] = {
            value: value2,
            oldValue: me.get(key)
          };
          meta.batchChanges[key] = value2;
          changed = true;
        }
      });
    } else {
      cmp = fieldMap[field] || ObjectHelper;
      if (!cmp.isEqual(meta.batchChanges[field], value2)) {
        wasSet[field] = {
          value: value2,
          oldValue: me.get(field)
        };
        meta.batchChanges[field] = value2;
        changed = true;
      }
    }
    if (changed) {
      me.generation++;
      if (!silent) {
        const event = {
          action: "update",
          record: me,
          records: [me],
          changes: wasSet
        };
        (_a5 = me.stores) == null ? void 0 : _a5.forEach(
          (store) => store.trigger("batchedUpdate", { ...event })
        );
        me.forEachLinked(
          (store, record) => store.trigger("batchedUpdate", { ...event, record, records: [record] })
        );
      }
    }
  }
  inSet(fieldNameOrObject, value2, silent, fromRelationUpdate, skipAccessors = false, validOnly = false, triggerBeforeUpdate = true) {
    var _a5, _b2, _c2, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p;
    const me = this, {
      data,
      meta,
      fieldMap,
      constructor
    } = me, {
      prototype: myProto2,
      childrenField,
      relations
    } = constructor, wasSet = {}, toSet = me.fieldToKeys(fieldNameOrObject, value2), keys = Object.keys(toSet).sort(constructor.fieldSorter), isSyncingDataOnLoad = (_a5 = me.stores) == null ? void 0 : _a5.some((store) => store.isSyncingDataOnLoad), suspendedModificationsTracking = (_b2 = me.stores) == null ? void 0 : _b2.some((store) => store.modificationsTracking > 0);
    if (!silent && triggerBeforeUpdate && !me.triggerBeforeUpdate(toSet)) {
      return null;
    }
    let changed = false, fieldDefinition, formulaProvider, formulaFieldName;
    if (typeof fieldNameOrObject === "object" && Object.keys(fieldNameOrObject).length === 1) {
      value2 = Object.values(fieldNameOrObject)[0];
      fieldNameOrObject = Object.keys(fieldNameOrObject)[0];
    }
    if (typeof fieldNameOrObject === "string" && (fieldDefinition = me.getFieldDefinition(fieldNameOrObject))) {
      if (fieldDefinition.formulaProviders) {
        const prefix = (_c2 = value2 == null ? void 0 : value2.match(fieldDefinition.inputPrefixRE)) == null ? void 0 : _c2[1];
        formulaFieldName = `#${fieldNameOrObject}`;
        formulaProvider = (_d = fieldDefinition.formulaProviders[prefix]) == null ? void 0 : _d.instance;
        if (!formulaProvider) {
          formulaProvider = (_e = fieldDefinition.getFormulaProvider) == null ? void 0 : _e.call(fieldDefinition, prefix);
        }
        keys.push(formulaFieldName);
        toSet[formulaFieldName] = value2;
      }
    }
    if (formulaProvider) {
      const { formulaRE } = formulaProvider, formulaSource = formulaRE ? (_h = (_g = (_f = value2.trim()).match) == null ? void 0 : _g.call(_f, formulaRE)) == null ? void 0 : _h[1] : value2;
      if (formulaSource) {
        const modelClass = me.constructor, dependentFields = modelClass.dependentFields || (modelClass.dependentFields = {}), prompt = formulaProvider.createFormula(formulaSource, me, me.firstStore), responseValue = formulaProvider.calculate(prompt);
        (_j = (_i = formulaSource == null ? void 0 : formulaSource.matchAll(formulaProvider.fieldMatchRE)) == null ? void 0 : _i.forEach) == null ? void 0 : _j.call(_i, ([, fieldName]) => {
          if (fieldName !== fieldNameOrObject) {
            (dependentFields[fieldNameOrObject] || (dependentFields[fieldNameOrObject] = {}))[fieldName] = 1;
          }
        });
        toSet[fieldNameOrObject] = responseValue;
        meta.pendingFormulae = (meta.pendingFormulae || 0) + 1;
        try {
          (_k = responseValue.then) == null ? void 0 : _k.call(responseValue, (v) => {
            meta.pendingFormulae = Math.max(0, meta.pendingFormulae - 1);
            me.set({
              [fieldNameOrObject]: v,
              [formulaFieldName]: value2
            });
          });
        } catch (err) {
          meta.pendingFormulae = Math.max(0, meta.pendingFormulae - 1);
          me.firstStore.trigger("formulaError", {
            record: me,
            field: fieldNameOrObject,
            error: err,
            formula: value2
          });
          me.set(fieldNameOrObject, "");
        }
      }
    }
    me.inSetting = true;
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (key === childrenField) {
        continue;
      }
      if (key.includes("[")) {
        const [, arrayFieldName, index, path] = key.match(arrayRe), field2 = me.fieldMap[arrayFieldName];
        if (field2 == null ? void 0 : field2.getAt) {
          const subRecord = (_m = field2.getAt(me, index)) != null ? _m : (_l = field2.onSubRecordMissingInSet) == null ? void 0 : _l.call(field2, me, index);
          if (subRecord == null) {
            throw new Error(`The store field '${arrayFieldName}' does not contain a record at index ${index} - can not set the data`);
          }
          if (subRecord.isModel) {
            subRecord.set(path, toSet[key]);
          } else {
            ObjectHelper.setPath(subRecord, path, toSet[key]);
          }
          continue;
        }
      }
      const complexKey = key.includes(".");
      if (relations && complexKey) {
        const [, relationName, prop] = key.match(nestedRe);
        if (relations[relationName]) {
          if (prop.includes(".")) {
            me[relationName].set(prop, toSet[key]);
          } else {
            me[relationName][prop] = toSet[key];
          }
          continue;
        }
      }
      const field = fieldMap[key], cmp = field || ObjectHelper, readOnly = field == null ? void 0 : field.readOnly, mapping = (_n = field == null ? void 0 : field.dataSource) != null ? _n : key, useProp = !skipAccessors && !field && key in myProto2 || (field == null ? void 0 : field.useProp), oldValue = useProp ? me[mapping] : (field == null ? void 0 : field.complexMapping) || complexKey ? ObjectHelper.getPath(data, mapping) : data[mapping], value3 = constructor.processField(key, toSet[key], me), val = toSet[key] = { value: value3 }, isPromise = ObjectHelper.isPromise(value3), relation = me.getRelationConfig(key);
      if (!readOnly && (isSyncingDataOnLoad && (field == null ? void 0 : field.bypassEqualityOnSyncDataset) || !cmp.isEqual(oldValue, value3)) && (!validOnly || value3 !== void 0)) {
        me.generation++;
        val.oldValue = (_p = (_o = field == null ? void 0 : field.getOldValue) == null ? void 0 : _o.call(field, me)) != null ? _p : oldValue;
        changed = true;
        if (key in meta.modified && cmp.isEqual(meta.modified[key], value3, false, true)) {
          Reflect.deleteProperty(meta.modified, key);
          if (me.isReverting) {
            me.data[mapping] = value3;
          }
        } else if (!me.ignoreBag && !suspendedModificationsTracking) {
          if (!(key in meta.modified) && !(oldValue == null && value3 == null) && !isPromise) {
            me.storeFieldChange(key, oldValue);
          }
          if (val.oldValue === _undefined) {
            Reflect.deleteProperty(val, "oldValue");
          }
        } else {
          if (suspendedModificationsTracking) {
            Reflect.deleteProperty(meta.modified, key);
          }
        }
        wasSet[key] = val;
        me.applyValue(useProp, mapping, value3, skipAccessors, field);
        if (relation && !fromRelationUpdate) {
          me.initRelation(relation);
          me.stores.forEach((store) => store.cacheRelatedRecord(me, value3, relation.relationName, val.oldValue));
        }
      }
    }
    if (changed) {
      me.afterChange(toSet, wasSet, silent, fromRelationUpdate, skipAccessors);
    }
    me.updateDependentFieldValues(fieldNameOrObject);
    me.inSetting = false;
    return changed ? wasSet : null;
  }
  // Provided as a hook for Engine to do what needs to be done which ever way a field value is changed
  applyValue(useProp, key, value2, skipAccessors, field) {
    var _a5, _b2;
    const me = this;
    if (((field == null ? void 0 : field.dataSource) || key) === me.constructor.idField) {
      me.$isPhantom = false;
      me._id = value2;
    }
    if (((field == null ? void 0 : field.dataSource) || key) === me.constructor.parentIdField) {
      if (((_a5 = me.firstStore) == null ? void 0 : _a5.tree) && !(((_b2 = me.parent) == null ? void 0 : _b2.isRoot) && (value2 == null || !me.firstStore.getById(value2)))) {
        (me.firstStore.getById(value2) || me.firstStore.rootNode).insertChild(me, null, false, {
          orderedParentIndex: { skip: true }
        });
      }
    } else if (useProp) {
      me[(field == null ? void 0 : field.name) || key] = value2;
    } else if (field == null ? void 0 : field.set) {
      field.set(value2, me.data, me);
    } else {
      let complexMapping = field == null ? void 0 : field.complexMapping;
      if (!field && key.includes(".")) {
        const nestedName = key.split(".")[0];
        field = me.constructor.fieldMap[nestedName];
        complexMapping = (field == null ? void 0 : field.complexMapping) || (field == null ? void 0 : field.type) === "object";
      }
      if (complexMapping) {
        ObjectHelper.setPath(me.data, key, value2);
      } else {
        me.data[key] = value2;
      }
    }
  }
  // skipAccessors argument is used in the engine override
  afterChange(toSet, wasSet, silent, fromRelationUpdate, skipAccessors) {
    const extraChanges = this.updateDependentFields(Object.keys(wasSet));
    if (extraChanges) {
      Object.assign(wasSet, extraChanges);
    }
    if (this.stores) {
      for (const store of this.stores) {
        store.onModelChange(this, toSet, wasSet, silent, fromRelationUpdate, skipAccessors);
      }
    }
    this.forEachLinked(
      (store, record) => store.onModelChange(record, toSet, wasSet, silent, fromRelationUpdate, skipAccessors)
    );
  }
  updateDependentFields(fields) {
    const changes = {};
    fields.forEach((fieldName) => {
      var _a5, _b2;
      (_b2 = (_a5 = this.getFieldDefinition(fieldName)) == null ? void 0 : _a5.dependentFields) == null ? void 0 : _b2.forEach((name) => {
        const field = this.fieldMap[name], value2 = this.refreshCalculatedField(field);
        changes[name] = value2;
      });
    });
    if (Object.keys(changes).length > 0) {
      return this.set(changes, void 0, true);
    }
    return changes;
  }
  /**
   * Recalculates the value of a calculated field
   * @param {Core.data.field.DataField|String} field The field instance or its name
   * @returns {*} The updated value of the field, or a Promise
   * @internal
   */
  refreshCalculatedField(field) {
    var _a5;
    const me = this;
    if (typeof field === "string") {
      field = me.getFieldDefinition(field);
    }
    const value2 = (_a5 = field.calculate) == null ? void 0 : _a5.fn.call(field, me);
    if (ObjectHelper.isPromise(value2)) {
      if (me.isConstructing) {
        me.data[field.name] = value2;
      } else {
        me[field.name] = value2;
      }
      value2.then((result) => me[field.name] = result);
    }
    return value2;
  }
  // Run fn for each store on each linked record
  forEachLinked(fn2) {
    for (const linked of this.$links) {
      for (const store of linked.stores) {
        fn2(store, linked);
      }
    }
  }
  /**
   * This yields `true` if this record is eligible for syncing with the server.
   * It can yield `false` if the record is in the middle of a {@link #property-isBatchUpdating batched update},
   * or if it is a {@link #property-isCreating tentative record} yet to be confirmed as a new addition.
   * @property {Boolean}
   * @readonly
   */
  get isPersistable() {
    return !this.isBatchUpdating && !this.isCreating;
  }
  /**
   * True if this model has any uncommitted changes.
   * @property {Boolean}
   * @readonly
   * @category Editing
   */
  get isModified() {
    return Boolean(this.meta.modified && Object.keys(this.meta.modified).length > 0);
  }
  get hasPersistableChanges() {
    return this.isPersistable && !ObjectHelper.isEmpty(this.rawModificationData);
  }
  /**
   * Returns `true` if this model has uncommitted changes for the provided field.
   * @param {String} fieldName Field name
   * @returns {Boolean} `true` if the field has an uncommitted change
   * @category Editing
   */
  isFieldModified(fieldName) {
    return this.isModified && fieldName in this.meta.modified;
  }
  /**
   * Returns the unmodified value of the field, as in the value it had after the last commit. If the field has not
   * been modified, the current value is returned.
   * @param {String} fieldName Field name
   * @returns {*} The unmodified value of the field, or its current value if not modified
   */
  getUnmodified(fieldName) {
    return this.isModified ? this.meta.modified[fieldName] : this.get(fieldName);
  }
  /**
   * Returns field value that should be persisted, or `undefined` if field is configured with `persist: false`.
   * @param {String|Core.data.field.DataField} nameOrField Name of the field to get value for, or its field definition
   * @private
   * @category Fields
   */
  getFieldPersistentValue(nameOrField) {
    const field = typeof nameOrField === "string" ? this.getFieldDefinition(nameOrField) : nameOrField, name = (field == null ? void 0 : field.name) || nameOrField;
    let result;
    if (!field || field.persist) {
      result = this.getValue(name);
      if (field == null ? void 0 : field.serialize) {
        result = field.serialize(result, this);
      }
    }
    return result;
  }
  /**
   * Get a map of the modified fields in form of an object. The field *names* are used as the property names
   * in the returned object.
   * @property {Object}
   * @readonly
   * @category Editing
   */
  get modifications() {
    const data = this.rawModifications;
    if (data && Object.keys(data).length) {
      data[this.constructor.idField] = this.id;
    }
    return data;
  }
  get rawModifications() {
    const me = this, data = {};
    if (!me.isModified) {
      return null;
    }
    let keySet = false;
    Object.keys(me.meta.modified).forEach((key) => {
      const value2 = me.getFieldPersistentValue(key);
      if (value2 !== _undefined) {
        data[key] = value2;
        keySet = true;
      }
    });
    return keySet ? data : null;
  }
  /**
   * Get a map of the modified fields in form of an object. The field´s {@link Core.data.field.DataField#config-dataSource}
   * is used as the property name in the returned object. The record´s id is always included.
   * @property {Object}
   * @readonly
   * @category Editing
   */
  get modificationData() {
    const data = this.rawModificationData;
    if (data && Object.keys(data).length) {
      ObjectHelper.setPath(data, this.constructor.idField, this.id);
    }
    return data;
  }
  /**
   * Returns a map of the modified persistable fields
   * @internal
   * @property {Object}
   * @category Editing
   */
  get rawModificationData() {
    const me = this, { fieldMap } = me.constructor, data = {};
    if (!me.isModified) {
      return null;
    }
    let keySet = false;
    Object.keys(me.meta.modified).forEach((fieldName) => {
      let field = fieldMap[fieldName], dataSource = field == null ? void 0 : field.dataSource;
      if (fieldName.includes(".")) {
        const topLevelFieldName = fieldName.match(nestedRe)[1];
        field = fieldMap[topLevelFieldName];
        dataSource = fieldName;
      }
      if (field == null ? void 0 : field.persist) {
        const value2 = me.getFieldPersistentValue(fieldName);
        if (value2 !== _undefined) {
          ObjectHelper.setPath(data, dataSource, value2);
          keySet = true;
        }
      }
    });
    return keySet ? data : null;
  }
  /**
   * Get a map of the modified data fields along with any {@link Core/data/field/DataField#config-alwaysWrite} fields,
   * in form of an object. The field´s *dataSource* is used as the property name in the returned object.
   * Used internally by AjaxStore / CrudManager when sending updates.
   * @property {Object}
   * @readonly
   * @category Editing
   */
  get modificationDataToWrite() {
    const me = this, { modificationData, constructor: ctor } = me, recordData = modificationData != null ? modificationData : {}, fields = ctor.alwaysWriteFields;
    let hasData = Boolean(modificationData);
    fields.forEach((field) => {
      ObjectHelper.setPath(recordData, field.dataSource, me.getFieldPersistentValue(field));
      hasData = true;
    });
    if (hasData && !modificationData) {
      ObjectHelper.setPath(recordData, ctor.idField, me.id);
    }
    return hasData ? recordData : null;
  }
  /**
   * Returns data for **all** {@link Core.data.field.DataField#config-persist persistable} fields in form of an
   * object, using dataSource if present.
   * @property {Object}
   * @internal
   * @readonly
   * @category Editing
   */
  get persistableData() {
    const me = this, data = {};
    me.fields.forEach((field) => {
      const value2 = me.getFieldPersistentValue(field);
      if (value2 !== _undefined) {
        if (field == null ? void 0 : field.complexMapping) {
          ObjectHelper.setPath(data, field.dataSource, value2);
        } else {
          data[field.dataSource] = value2;
        }
      }
    });
    return data;
  }
  get dataByFieldName() {
    const { data } = this;
    return this.fields.reduce((result, field) => {
      if (ObjectHelper.hasPath(data, field.dataSource)) {
        result[field.name] = data[field.dataSource];
      }
      return result;
    }, {});
  }
  /**
   * True if this models changes are currently being committed.
   * @property {Boolean}
   * @category Editing
   */
  get isCommitting() {
    return Boolean(this.meta.committing);
  }
  /**
   * Clears tracked changes, used on commit. Does not revert changes.
   * @param {Boolean} [includeDescendants] Supply `false` to not clear node descendants
   * @category Editing
   */
  clearChanges(includeDescendants) {
    var _a5;
    this.internalClearChanges(includeDescendants);
    (_a5 = this.stores) == null ? void 0 : _a5.forEach((store) => store.trigger("clearChanges", { record: this }));
  }
  internalClearChanges(includeDescendants = true, removeFromStoreChanges = true, changes = null, clearNonPersistable = false) {
    var _a5, _b2, _c2;
    const me = this, { meta } = me;
    if (changes) {
      const { modified } = meta;
      for (const key in changes) {
        delete modified[key];
      }
      for (const key in modified) {
        if (
          // If there's a store field modified, we won't receive those changes here. So instead we need to go
          // over modified fields, find stores and remove those keys if stores' changes are null
          ((_a5 = modified[key]) == null ? void 0 : _a5.isStore) && !modified[key].changes || // Remove non-persistable fields
          clearNonPersistable && !((_b2 = me.getFieldDefinition(key)) == null ? void 0 : _b2.persist)
        ) {
          delete modified[key];
        }
      }
    } else {
      meta.modified = Object.assign({}, meta.modifiedMaterialized);
      meta.modifiedMaterialized = null;
    }
    const noChanges = ObjectHelper.isEmpty(meta.modified);
    meta.committing = false;
    if (removeFromStoreChanges) {
      (_c2 = me.stores) == null ? void 0 : _c2.forEach((store) => {
        noChanges && store.modified.remove(me);
        store.added.remove(me);
        if (includeDescendants) {
          const descendants = store.collectDescendants(me).all;
          store.added.remove(descendants);
          noChanges && store.modified.remove(descendants);
        }
      });
    }
  }
  storeFieldChange(key, oldValue) {
    var _a5;
    const { meta } = this;
    meta.modified[key] = oldValue;
    if (this.isBeingMaterialized) {
      meta.modifiedMaterialized = (_a5 = meta.modifiedMaterialized) != null ? _a5 : {};
      meta.modifiedMaterialized[key] = oldValue;
    }
  }
  /**
   * Reverts changes in this back to their original values.
   * @privateparam {Boolean} [silent] Specify `true` to not trigger events.
   * @category Editing
   */
  revertChanges(silent = false) {
    this.isReverting = true;
    this.set(this.meta.modified, _undefined, silent);
    this.isReverting = false;
  }
  setByDataSource(rawChanges) {
    var _a5;
    const me = this, { fieldDataSourceMap } = me.constructor, rawChangesSimplePaths = ObjectHelper.pathifyKeys(rawChanges, fieldDataSourceMap), changes = {};
    for (const dataSource in rawChangesSimplePaths) {
      const field = fieldDataSourceMap[dataSource], propName = (_a5 = field == null ? void 0 : field.name) != null ? _a5 : dataSource;
      changes[propName] = rawChangesSimplePaths[dataSource];
    }
    return me.set(changes);
  }
  applyChangeset(rawChanges, phantomIdField = "$PhantomId", remote = true) {
    var _a5;
    const me = this, modelClass = me.constructor, {
      idField,
      fieldDataSourceMap
    } = modelClass, rawChangesSimplePaths = ObjectHelper.pathifyKeys(rawChanges, fieldDataSourceMap), ownChangesSimplePaths = ObjectHelper.pathifyKeys(me.modificationData), changes = {}, idChanged = phantomIdField in rawChanges && !(idField in rawChanges);
    if (!idChanged && phantomIdField in rawChangesSimplePaths) {
      delete rawChangesSimplePaths[phantomIdField];
    }
    for (const dataSource in rawChangesSimplePaths) {
      const field = fieldDataSourceMap[dataSource], propName = (_a5 = field == null ? void 0 : field.name) != null ? _a5 : dataSource;
      if (remote) {
        changes[propName] = rawChangesSimplePaths[dataSource];
      } else if (ownChangesSimplePaths[dataSource] === rawChangesSimplePaths[dataSource]) {
        changes[propName] = rawChangesSimplePaths[dataSource];
      }
    }
    const wasSet = me.set(changes);
    me.internalClearChanges(false, true, changes);
    return wasSet ? Object.entries(wasSet).reduce((result, [field, change]) => {
      result[field] = change.value;
      return result;
    }, {}) : {};
  }
  //endregion
  //region Id
  /**
   * Gets the records internalId. It is assigned during creation, guaranteed to be globally unique among models.
   * @property {Number}
   * @category Identification
   */
  get internalId() {
    return this._internalId;
  }
  /**
   * Returns true if the record is new and has not been persisted (and received a proper id).
   * @property {Boolean}
   * @readonly
   * @category Identification
   */
  get isPhantom() {
    return this.id === "" || this.id == null || this.hasGeneratedId || Boolean(this.$isPhantom);
  }
  get isModel() {
    return true;
  }
  /**
   * Checks if record has a generated id.
   *
   * New records are assigned a generated id based on a UUID (starting with `_generated`), which is intended to be
   * temporary and should be replaced by the backend on commit.
   *
   * @property {Boolean}
   * @category Identification
   */
  get hasGeneratedId() {
    return typeof this.id === "string" && this.id.startsWith("_generated");
  }
  /**
   * Generates an id for a new record (a phantom id), based on a UUID by default.
   *
   * This function can be overridden to provide custom id generation logic.
   *
   * ```javascript
   * let idCounter = 0;
   * // Override the default logic with app specific
   * Model.generateId = () => `id${new Date().getTime()}${idCounter++}`;
   * ```
   *
   * @param {String} text Text used as optional prefix for the id, defaults to the name of the class
   * @returns {String|Number} A generated id
   */
  static generateId(text = this.$$name) {
    return `_generated${text}_${StringHelper.generateUUID()}`;
  }
  /**
   * Generates an id for a new record (a phantom id), based on a UUID (starting with `_generated`).
   *
   * Generated ids are intended to be temporary and should be replaced by the backend on commit.
   *
   * @category Identification
   * @returns {String}
   */
  generateId() {
    return this.constructor.generateId();
  }
  /**
   * Gets the id of specified model or model data object, or the value if passed string/number.
   * @param {Core.data.Model|String|Number} model
   * @returns {String|Number} id
   * @category Identification
   */
  static asId(model) {
    return (model == null ? void 0 : model.isModel) ? model.id : ObjectHelper.isObject(model) ? model[this.fieldMap.id.dataSource] : model;
  }
  //endregion
  //region JSON
  /**
   * Get the records data as a json string.
   *
   * ```javascript
   * const record = new Model({
   *     title    : 'Hello',
   *     children : [
   *         ...
   *     ]
   * });
   *
   * const jsonString = record.json;
   *
   * //jsonString:
   * '{"title":"Hello","children":[...]}'
   * ```
   *
   * @member {String}
   * @category JSON
   */
  get json() {
    return StringHelper.safeJsonStringify(this);
  }
  /**
   * Used by `JSON.stringify()` to correctly convert this record to json.
   *
   * In most cases no point in calling it directly.
   *
   * ```
   * // This will call `toJSON()`
   * const json = JSON.stringify(record);
   * ```
   *
   * If called manually, the resulting object is a clone of `record.data` + the data of any children:
   *
   * ```
   * const record = new Model({
   *     title    : 'Hello',
   *     children : [
   *         ...
   *     ]
   * });
   *
   * const jsonObject = record.toJSON();
   *
   * // jsonObject:
   * {
   *     title : 'Hello',
   *     children : [
   *         ...
   *     ]
   * }
   * ```
   *
   * @returns {Object}
   * @category JSON
   */
  toJSON() {
    const { children, unfilteredChildren } = this, jsonData = this.persistableData;
    if (unfilteredChildren || children) {
      jsonData[this.constructor.childrenField] = (unfilteredChildren || children).map((c) => c.toJSON());
    }
    return jsonData;
  }
  /**
   * Represent the record as a string, by default as a JSON string. Tries to use an abbreviated version of the
   * object's data, using id + name/title/text/label/description. If no such field exists, the full data is used.
   *
   * ```javascript
   * const record = new Model({ id : 1, name : 'Steve Rogers', alias : 'Captain America' });
   * console.log(record.toString()); // logs { "id" : 1, "name" : "Steve Rogers" }
   * ```
   *
   * @returns {String}
   * @category JSON
   */
  toString() {
    const me = this, nameField = abbreviationFields.find((field) => field in me.constructor.fieldMap), data = nameField ? { [me.constructor.idField]: me.id, [nameField]: me[nameField] } : me.data;
    return StringHelper.safeJsonStringify(data);
  }
  //endregion
  //region Batch
  /**
   * True if this Model is currently batching its changes.
   * @property {Boolean}
   * @readonly
   * @category Editing
   */
  get isBatchUpdating() {
    return Boolean(this.batching);
  }
  /**
   * Returns `true` if this Model currently has outstanding batched changes for the specified field name.
   * @param {String} fieldName The field name to check for batched updates on.
   * @returns {Boolean}
   * @category Editing
   */
  hasBatchedChange(fieldName) {
    var _a5, _b2;
    return (_b2 = (_a5 = this.meta) == null ? void 0 : _a5.batchChanges) == null ? void 0 : _b2[fieldName];
  }
  /**
   * Begin a batch, which stores changes and commits them when the batch ends.
   * Prevents events from being fired during batch.
   *
   * ```javascript
   * record.beginBatch();
   * record.name = 'Mr Smith';
   * record.team = 'Golden Knights';
   * record.endBatch();
   * ```
   *
   * Please note that you can also set multiple fields in a single call using {@link #function-set}, which in many
   * cases can replace using a batch:
   *
   * ```javascript
   * record.set({
   *   name : 'Mr Smith',
   *   team : 'Golden Knights'
   * });
   * ```
   * @category Editing
   * @privateparam {Boolean} silentUpdates Suppress firing the `batchUpdatedEvent`
   */
  beginBatch(silentUpdates = false) {
    const me = this;
    if (!me.batching) {
      me.batching = 0;
      me.meta.batchChanges = {};
    }
    if (silentUpdates) {
      me.$silenceBatch = (me.$silenceBatch || 0) + 1;
    }
    me.batching++;
  }
  /**
   * End a batch, triggering events if data has changed.
   * @param {Boolean} [silent] Specify `true` to not trigger events. If event is recurring, occurrences won't be updated automatically.
   * @privateparam {Boolean} [silent=false] Specify `true` to not trigger events. If event is recurring, occurrences won't be updated automatically.
   * @privateparam {Boolean} [triggerBeforeUpdate=true]
   * @category Editing
  */
  endBatch(silent = false, skipAccessors = false, triggerBeforeUpdate = true) {
    const me = this, { parentIdField } = me.constructor;
    if (!me.batching) {
      return;
    }
    me.batching--;
    me.$silenceBatch && me.$silenceBatch--;
    if (me.batching > 0) {
      return;
    }
    if (!ObjectHelper.isEmpty(me.meta.batchChanges)) {
      let { batchChanges } = me.meta;
      me.meta.batchChanges = null;
      if (batchChanges[parentIdField]) {
        batchChanges = { ...batchChanges };
        me.parentId = batchChanges[parentIdField];
        delete batchChanges[parentIdField];
      }
      me.set(batchChanges, _undefined, silent, false, skipAccessors, void 0, triggerBeforeUpdate);
    }
  }
  /**
   * Cancels current batch operation. Any changes during the batch are discarded.
   * @category Editing
   */
  cancelBatch() {
    var _a5;
    if (this.batching) {
      const me = this, { batchChanges } = me.meta, wasSet = {};
      me.batching = null;
      me.meta.batchChanges = null;
      me.generation++;
      if (!me.$silenceBatch) {
        Object.entries(batchChanges).forEach(([key, oldValue]) => {
          wasSet[key] = {
            oldValue,
            value: me[key]
          };
        });
        const event = {
          action: "update",
          record: me,
          records: [me],
          changes: wasSet
        };
        (_a5 = me.stores) == null ? void 0 : _a5.forEach(
          (store) => (
            // Cant use event directly, it will get polluted with `source` and then have that incorrect for
            // following calls
            store.trigger("batchedUpdate", { ...event })
          )
        );
        me.forEachLinked(
          (store, record) => store.trigger("batchedUpdate", { ...event, record, records: [record] })
        );
      }
      me.$silenceBatch && me.$silenceBatch--;
    }
  }
  //endregion
  //region Events
  /**
   * Triggers beforeUpdate event for each store and checks if changes can be made from event return value.
   * @param {Object} changes Data changes
   * @returns {Boolean} returns true if data changes are accepted
   * @private
   */
  triggerBeforeUpdate(changes) {
    var _a5;
    return !((_a5 = this.stores) == null ? void 0 : _a5.some((s) => s.trigger("beforeUpdate", { record: this, changes }) === false));
  }
  //endregion
  //region Additional functionality
  /**
   * For copied records, this property links to the original model instance from which it was copied.
   * @member {Core.data.Model} copyOf
   * @readonly
   * @category Editing
   */
  /**
   * Makes a copy of this model, assigning the specified id or a generated id and also allowing you to pass field
   * values to the created copy. A {@link #property-copyOf} property is set on the copy to reference the original
   * record.
   *
   * ```
   * const record = new Model({ name : 'Super model', hairColor : 'Brown' });
   * const clone = record.copy({ name : 'Super model clone' });
   * ```
   * @param {Number|String|Object} [newIdOrData] The id for the copied instance, or any field values to apply
   * (overriding the values from the source record). If no id provided, one will be auto-generated
   * @param {Boolean} [deep] True to also clone children
   * @returns {Core.data.Model} Copy of this model
   * @category Editing
   */
  copy(newIdOrData = null, deep) {
    const me = this, data = ObjectHelper.clone(me.data), idField = me.constructor.idField, useDeep = ObjectHelper.isObject(deep) ? deep.deep : deep;
    let id2;
    if (newIdOrData && typeof newIdOrData === "object") {
      id2 = newIdOrData[idField];
      Object.assign(data, newIdOrData);
    } else {
      id2 = newIdOrData;
    }
    if (useDeep && me.children) {
      data.children = me.children.map((child) => child.copy(newIdOrData === false ? false : void 0, deep));
    } else {
      delete data.children;
      delete data.expanded;
    }
    if (me.$meta.fields.initable.length > 0) {
      const json = me.toJSON();
      for (const field of me.$meta.fields.initable) {
        data[field.name] = json[field.name];
      }
    }
    if (newIdOrData !== false) {
      data[idField] = id2 || me.generateId(me.firstStore);
    }
    const copy = new me.constructor(data, null, null, false, true);
    copy.originalInternalId = me.internalId;
    copy.copyOf = me;
    return copy;
  }
  // Copies data using the real field names to trigger setters
  copyData(fromRecord, raw, silent) {
    const propertiesAndValues = {}, skipFields = this.skipCopyFields || {};
    fromRecord.allFields.forEach(({ name: fieldName }) => {
      if (fieldName !== fromRecord.constructor.idField && !(fieldName in skipFields)) {
        propertiesAndValues[fieldName] = raw ? fromRecord.get(fieldName) : fromRecord.getValue(fieldName);
      }
    });
    this.set(propertiesAndValues, null, silent);
  }
  /**
   * Removes this record from all stores (and in a tree structure, also from its parent if it has one).
   * @param {Boolean} [silent] Specify `true` to not trigger events. If the event is recurring, occurrences won't be
   * updated automatically.
   * @category Editing
   */
  remove(silent = false) {
    const me = this, { parent, stores: stores2 } = me;
    if (parent) {
      parent.removeChild(me, false, silent, { unfiltered: true });
    } else if ((stores2 == null ? void 0 : stores2.length) && !me.isSpecialRow) {
      const storesCopy = stores2.slice();
      storesCopy.forEach((s) => s.remove(me, silent, false, true));
    }
  }
  // Called by stores before removing the record from the store. Returning false prevents the removal (overridden in
  // ModelLink.js)
  beforeRemove(stores2, records) {
    return super.beforeRemove(stores2, records);
  }
  //endregion
  //region Validation
  /**
   * Check if record has valid data. Default implementation returns true, override in your model to do actual validation.
   * @property {Boolean}
   * @category Editing
   */
  get isValid() {
    return true;
  }
  //endregion
  //region Store
  /**
   * Get the first store that this model is assigned to.
   * @property {Core.data.Store}
   * @readonly
   * @category Misc
   */
  get firstStore() {
    var _a5;
    return (_a5 = this.stores) == null ? void 0 : _a5[0];
  }
  /**
   * Joins this record and any children to specified store, if not already joined.
   * @internal
   * @param {Core.data.Store} store Store to join
   * @category Misc
   */
  joinStore(store) {
    var _a5, _b2;
    const me = this;
    if (!me.stores || !me.stores.includes(store)) {
      super.joinStore(store);
      store.register(me);
      if (!me.stores) {
        me.stores = [store];
      } else {
        me.stores.push(store);
      }
      if (me.unjoinedStores && me.unjoinedStores.includes(store)) {
        me.unjoinedStores.splice(me.unjoinedStores.indexOf(store), 1);
      }
      ((_a5 = me.children) == null ? void 0 : _a5.length) && me.children.forEach((child) => child.joinStore(store));
      me.constructor.exposedRelations && me.initRelations();
      if (store.tree && !me.isRoot && !((_b2 = store.stm) == null ? void 0 : _b2.isRestoring)) {
        me.instanceMeta(store.id).collapsed = !me.expanded;
      }
    }
  }
  /**
   * Unjoins this record and any children from specified store, if already joined.
   * @internal
   * @param {Core.data.Store} store Store to join
   * @param {Boolean} [isReplacing] `true` if this record is being replaced
   * @category Misc
   */
  unjoinStore(store, isReplacing = false) {
    var _a5, _b2, _c2;
    const me = this, { stores: stores2 } = me;
    if (stores2 == null ? void 0 : stores2.includes(store)) {
      if (!store.isDestroying) {
        store.unregister(me);
        if (!me.unjoinedStores) {
          me.unjoinedStores = [store];
        } else {
          me.unjoinedStores.push(store);
        }
      }
      (_b2 = (_a5 = me.unfilteredChildren || me.children) == null ? void 0 : _a5.forEach) == null ? void 0 : _b2.call(_a5, (child) => child.unjoinStore(store, isReplacing));
      stores2.splice(stores2.indexOf(store), 1);
      (_c2 = super.unjoinStore) == null ? void 0 : _c2.call(this, store, isReplacing);
      store.uncacheRelatedRecord(me);
      if (me.meta.map && !store.stm) {
        delete me.meta.map[store.id];
      }
    }
  }
  /**
   * Returns `true` if this record is contained in the specified store, or in any store if store param is omitted.
   * @internal
   * @param {Core.data.Store} store Store to check
   * @returns {Boolean}
   * @category Misc
   */
  isPartOfStore(store) {
    var _a5;
    if (store) {
      return store.includes(this);
    }
    return ((_a5 = this.stores) == null ? void 0 : _a5.length) > 0;
  }
  /**
   * Returns true if this record is not part of any store.
   * @property {Boolean}
   * @readonly
   * @internal
   */
  get isRemoved() {
    return !this.isPartOfStore();
  }
  //endregion
  /**
   * Defines if the given event field should be manually editable in UI.
   * You can override this method to provide your own logic.
   *
   * @param {String} fieldName Name of the field
   * @returns {Boolean|undefined} Returns `true` if the field is editable, `false` if it is not and `undefined` if the model has no such field.
   */
  isEditable(fieldName) {
    const fieldDef = this.getFieldDefinition(fieldName);
    if (fieldDef == null ? void 0 : fieldDef.calculated) {
      return false;
    }
    if (this.readOnly) {
      return fieldName === "readOnly";
    }
    return fieldDef ? true : void 0;
  }
  //region Per instance meta
  /**
   * Used to set per external instance meta data. For example useful when using a record in multiple grids to store some state
   * per grid.
   * @param {String|Object} instanceOrId External instance id or the instance itself, if it has id property
   * @private
   * @category Misc
   */
  instanceMeta(instanceOrId) {
    var _a5;
    const { meta } = this, id2 = instanceOrId.id || instanceOrId;
    return ((_a5 = meta.map) == null ? void 0 : _a5[id2]) || this.setInstanceMeta(id2, {});
  }
  setInstanceMeta(instanceOrId, value2) {
    const { meta } = this, id2 = instanceOrId.id || instanceOrId;
    if (!meta.map) {
      meta.map = {};
    }
    return meta.map[id2] = value2;
  }
  /**
   * When called on a group header row returns list of records in that group. Returns `undefined` otherwise.
   * @member {Core.data.Model[]|undefined} groupChildren
   * @category Grouping
   * @readonly
   */
  /**
   * Returns true for a group header record
   * @member {Boolean}
   * @category Grouping
   * @readonly
   */
  get isGroupHeader() {
    return "groupRowFor" in this.meta;
  }
  get isGroupFooter() {
    return "groupFooterFor" in this.meta;
  }
  get isSpecialRow() {
    var _a5;
    return Boolean((_a5 = this.meta) == null ? void 0 : _a5.specialRow);
  }
  get $original() {
    return this.isLinked ? this.proxyMeta.originalRecord : this;
  }
  //endregion
  static get nonPersistableFields() {
    const me = this;
    if (!me._nonPersistableFields) {
      me._nonPersistableFields = {};
      me.allFields.forEach((field) => {
        if (!field.persist || field.calculated) {
          me._nonPersistableFields[field.name] = 1;
        }
      });
    }
    return me._nonPersistableFields;
  }
  static get alwaysWriteFields() {
    const me = this;
    if (!me._alwaysWriteFields) {
      me._alwaysWriteFields = me.allFields.filter((field) => field.alwaysWrite);
    }
    return me._alwaysWriteFields;
  }
  // Id with spaces and dots replaced by -, for safe usage as an id in DOM
  get domId() {
    return typeof this.id === "string" ? this.id.replace(/[ .]/g, "-") : this.id;
  }
  //region Extract config
  // These functions are not meant to be called by any code other than Base#getCurrentConfig()
  // Convert custom modelClass to string, keeping custom fields
  static toJavaScriptValue(options) {
    const { names: names2 } = this.$meta, className = names2[names2.length - 2], superName = names2[names2.length - 3];
    return `class ${className} extends ${superName} { static fields = ${StringHelper.toJavaScriptValue(this.fields, options)}; }`;
  }
  // Get fields current values
  getCurrentConfig(options) {
    const { data, children } = this, { defaultValues, applyConfigs } = this.constructor, result = applyConfigs ? super.getCurrentConfig(options) : {};
    if (result) {
      for (const field of this.fields) {
        if (field.persist) {
          const value2 = ObjectHelper.getPath(data, field.dataSource);
          if (value2 != null && !field.isEqual(value2, defaultValues[field.name])) {
            ObjectHelper.setPath(result, field.dataSource, Base.processConfigValue(value2, options));
          }
        }
      }
      if (children) {
        if (Array.isArray(children)) {
          result.children = [];
          for (const child of children) {
            result.children.push(child.getCurrentConfig(options));
          }
        } else {
          result.children = children;
        }
      }
      if (this.hasGeneratedId) {
        delete result.id;
      }
      delete result.parentId;
      delete result.parentIndex;
    }
    return result;
  }
  //endregion
};
__publicField(_Model, "$name", "Model");
/**
 * Override in a subclass of Model to define relations to records in other stores.
 *
 * Always defined on the "one" side, not the "many" side.
 *
 * Expects an object where keys are relation names and values are {@link #typedef-RelationConfig relation configs}.
 *
 * This snippet will define a relation called `team`, allowing access to the foreign record via `player.team`. It
 * will point to a record in the `teamStore` (must be available as `record.firstStore.teamStore)` with an id
 * matching the players `teamId` field. The team record in turn, will have a field called `players` which is a
 * collection of all players in the team.
 *
 * ```javascript
 * class Player extends Model {
 *     static relations = {
 *         // Define a relation between a player and a team
 *         team : {
 *             foreignKey            : 'teamId',
 *             foreignStore          : 'teamStore',
 *             relatedCollectionName : 'players'
 *         }
 *     }
 * }
 *
 * const teamStore = new Store({
 *     data : [
 *         { id : 1, name : 'Brynas' },
 *         { id : 2, name : 'Leksand' }
 *     ]
 * });
 *
 * const playerStore = new Store({
 *     modelClass : Player,
 *     // Matches foreignStore, allowing records of playerStore to find the related store
 *     teamStore,
 *     data       : [
 *         // teamId is specified as foreignKey, will be used to match the team
 *         { id : 1, name : 'Nicklas Backstrom', teamId : 1  },
 *         { id : 2, name : 'Elias Lindholm',   teamId : 1  },
 *         { id : 3, name : 'Filip Forsberg',  teamId : 2  }
 *     ],
 * }
 *
 * playerStore.first.team.name // > Brynas
 * playerStore.last.team.name // > Leksand
 * teamStore.first.players // > [nick, elias]
 * teamStore.last.players // > [filip]
 * ```
 *
 * To access the related record from the many side, use dot notation for the field name. For example in a Grid
 * column:
 *
 * ```javascript
 * const grid = new Grid({
 *    store : playerStore,
 *    columns : [
 *        { field : 'name', text : 'Name' },
 *        { field : 'team.name', text : 'Team' }
 *    ]
 * });
 * ```
 *
 * @member {Object<String,RelationConfig>} relations
 * @static
 */
__publicField(_Model, "relations", null);
var Model = _Model;
Model._idField = "id";
Model._internalIdCounter = 1;
Model._assignedIdField = false;
Model.exposeProperties();
Model._$name = "Model";

// ../Core/lib/Core/helper/WalkHelper.js
var WalkHelper = class {
  /**
   * Pre-walks any hierarchical data structure
   *
   * @param {Object} data Walking starting point
   * @param {Function} childrenFn Function to return `data` children entries in an array
   *                   or null if no children exists for the entry
   * @param {Function} fn Function to call on each entry
   */
  static preWalk(data, childrenFn, fn2) {
    const walkStack = [data];
    let node, children;
    while (walkStack.length) {
      node = walkStack.pop();
      fn2(node);
      children = childrenFn(node);
      if (children) {
        walkStack.push.apply(walkStack, children.slice().reverse());
      }
    }
  }
  /**
   * Pre-walks any hierarchical data structure while the passed `fn` returns true
   *
   * @param {Object} data Walking starting point
   * @param {Function} childrenFn Function to return `data` children entries in an array
   *                   or null if no children exists for the entry
   * @param {Function} fn Function to call on each entry. If return false here, it aborts the walk.
   * @returns {Boolean} `false` if the walk was aborted.
   */
  static walkWhile(data, childrenFn, fn2) {
    const walkStack = Array.isArray(data) ? data.slice() : [data];
    let node, children;
    while (walkStack.length) {
      node = walkStack.pop();
      if (!fn2(node)) {
        return false;
      }
      children = childrenFn(node);
      if (Array.isArray(children)) {
        for (let i = 0; i < children.length; i++) {
          walkStack.push(children[i]);
        }
      }
    }
    return true;
  }
  /**
   * Pre-walks any hierarchical data structure, passing along a link to the parent node
   *
   * @param {Object} data Walking starting point
   * @param {Function} childrenFn Function to return `data` children entries in an array
   *                   or null if no children exists for the entry
   * @param {Function} fn Function to call on each entry, called with `parent` and `node`
   */
  static preWalkWithParent(data, childrenFn, fn2) {
    const walkStack = [{ node: data, parent: null }];
    while (walkStack.length) {
      const { parent, node } = walkStack.pop();
      fn2(parent, node);
      const children = childrenFn(node);
      if (Array.isArray(children)) {
        for (let i = children.length - 1; i >= 0; i--) {
          walkStack.push({ node: children[i], parent: node });
        }
      }
    }
  }
  /**
   * Pre-walk unordered.
   *
   * Like {@link #function-preWalk-static} but doesn't reverse children before walk,
   * thus children will be walked last child first - first child last
   *
   * @param {Object} data Walking starting point
   * @param {Function} childrenFn Function to return `data` children entries in an array
   *                   or null if no children exists for the entry
   * @param {Function} fn Function to call on each entry
   */
  static preWalkUnordered(data, childrenFn, fn2) {
    const walkStack = [data];
    let node, children;
    while (walkStack.length) {
      node = walkStack.pop();
      fn2(node);
      children = childrenFn(node);
      if (children) {
        walkStack.push.apply(walkStack, children);
      }
    }
  }
  /**
   * Post-walks any hierarchical data structure
   *
   * @param {Object} data Walking starting point
   * @param {Function} childrenFn Function to return `data` children entries in an array
   *                   or null if no children exists for the entry
   * @param {Function} fn Function to call on each entry
   */
  static postWalk(data, childrenFn, fn2) {
    const visited = /* @__PURE__ */ new Map(), walkStack = [data];
    let node, children;
    while (walkStack.length) {
      node = walkStack[walkStack.length - 1];
      if (visited.has(node)) {
        fn2(node);
        walkStack.pop();
      } else {
        children = childrenFn(node);
        if (children) {
          for (let i = children.length - 1; i >= 0; i--) {
            walkStack.push(children[i]);
          }
        }
        visited.set(node, node);
      }
    }
  }
  /**
   * Pre-/Post-walks any hierarchical data structure calling inFn each node when it walks in,
   * and outFn when it walks out.
   *
   * @param {Object} data Walking starting point
   * @param {Function} childrenFn Function to return `data` children entries in an array
   *                   or null if no children exists for the entry
   * @param {Function} inFn  Function to call on each entry upon enter
   * @param {Function} outFn Function to call on each entry upon exit
   */
  static prePostWalk(data, childrenFn, inFn, outFn) {
    const visited = /* @__PURE__ */ new Map(), walkStack = [data];
    let node, children;
    while (walkStack.length) {
      node = walkStack[walkStack.length - 1];
      if (visited.has(node)) {
        outFn(node);
        walkStack.pop();
      } else {
        inFn(node);
        children = childrenFn(node);
        if (children) {
          for (let i = children.length - 1; i >= 0; i--) {
            walkStack.push(children[i]);
          }
        }
        visited.set(node, node);
      }
    }
  }
};
WalkHelper._$name = "WalkHelper";

// ../Core/lib/Core/helper/util/Formatter.js
var hasOwn5 = Object.hasOwn || ((obj, key) => Object.prototype.hasOwnProperty.call(obj, key));
var cacheKey = null;
function setParser(me, parser) {
  Object.defineProperty(me, "parser", {
    value: parser
  });
  return parser;
}
var Default = class {
  constructor(formatter) {
    this.formatter = formatter;
  }
  format(value2) {
    return this.formatter.defaultFormat(value2);
  }
  parse(value2, strict) {
    return this.formatter.defaultParse(value2, strict);
  }
  resolvedOptions() {
    return null;
  }
};
var Formatter = class {
  static get(format2) {
    if (format2 == null) {
      return this.NULL;
    }
    if (format2 instanceof this) {
      return format2;
    }
    const key = typeof format2 === "string" ? format2 : JSON.stringify(format2), cache = this.cache;
    let fmt = cache.get(key);
    if (!fmt) {
      cacheKey = key;
      fmt = new this(format2);
      cache.set(key, fmt);
    }
    return fmt;
  }
  static get cache() {
    return hasOwn5(this, "_cache") && this._cache || (this._cache = /* @__PURE__ */ new Map());
  }
  static get NULL() {
    return hasOwn5(this, "_null") ? this._null : this._null = new this(null);
  }
  constructor(config) {
    const me = this, { standardOptions } = me.constructor;
    me.cacheKey = cacheKey;
    cacheKey = null;
    me.initialize();
    if (config === null) {
      me.formatter = new Default(me);
    } else {
      me.configure(config);
      for (const [key, value2] of Object.entries(me.resolvedOptions())) {
        if (value2 != null && standardOptions.includes(key)) {
          me[key] = value2;
        }
      }
    }
  }
  get parser() {
    return setParser(this, new this.constructor.Parser(this));
  }
  defaultFormat(value2) {
    return value2 == null ? value2 : String(value2);
  }
  defaultParse(value2) {
    return value2;
  }
  format(value2) {
    return value2 == null ? value2 : this.formatter.format(value2);
  }
  parse(value2, strict) {
    return value2 == null ? value2 : this.parser.parse(value2, strict);
  }
  parseStrict(value2) {
    return this.parse(value2, true);
  }
  resolvedOptions() {
    return this.formatter.resolvedOptions();
  }
};
__publicField(Formatter, "standardOptions", Object.freeze([]));
Formatter._$name = "Formatter";

// ../Core/lib/Core/helper/util/NumberFormat.js
var escapeRegExp = StringHelper.escapeRegExp;
var digitsRe2 = /[\d+-]/g;
var newFormatter = (locale6, config) => new Intl.NumberFormat(locale6 || void 0, config);
var numFormatRe = /^(?:([$])\s*)?(?:(\d+)>)?\d+(,\d+)?(?:\.((\d*)(?:#*)|[*]))?(?:\s*([%])?)?$/;
var unicodeMinus = "\u2212";
var NumberParser = class {
  constructor(formatter) {
    const me = this, locale6 = formatter.locale, numFmt = newFormatter(locale6, {
      maximumFractionDigits: 3
    }), currency = formatter.is.currency ? me._decodeStyle(locale6, {
      style: "currency",
      currency: formatter.currency,
      currencyDisplay: formatter.currencyDisplay
    }) : null, percent = formatter.is.percent ? me._decodeStyle(locale6, {
      style: "percent"
    }) : null, decimal = numFmt.format(1.2).replace(digitsRe2, "")[0], grouper = numFmt.format(1e9).replace(digitsRe2, "")[0] || "";
    Object.assign(me, { currency, decimal, formatter, grouper, percent });
    me.decimal = decimal;
    me.decimalRe = escapeRegExp(decimal, "g");
    me.grouper = grouper;
    me.stripRe = new RegExp(
      `(?:\\s+|${escapeRegExp(grouper)})` + (currency ? `|(?:${escapeRegExp(currency.text)})` : "") + (percent ? `|(?:${escapeRegExp(percent.text)})` : ""),
      "g"
    );
  }
  decimalPlaces(value2) {
    value2 = value2.replace(this.stripRe, "");
    const dot = value2.indexOf(this.decimal) + 1;
    return dot && value2.length - dot;
  }
  parse(value2, strict) {
    if (typeof value2 === "string") {
      value2 = value2.replace(this.stripRe, "").replace(this.decimalRe, ".").replace(unicodeMinus, "-");
      value2 = strict ? Number(value2) : parseFloat(value2);
      if (this.formatter.is.percent) {
        value2 /= 100;
      }
    }
    return value2;
  }
  _decodeStyle(locale6, fmtDef) {
    const fmt = newFormatter(locale6, fmtDef), decFmt = newFormatter(locale6, Object.assign(
      fmt.resolvedOptions(),
      { style: "decimal" }
    )), zero = fmt.format(0), zeroDec = decFmt.format(0);
    return {
      suffix: zero.startsWith(zeroDec),
      text: zero.replace(zeroDec, "").trim()
    };
  }
};
var _NumberFormat = class _NumberFormat extends Formatter {
  constructor() {
    super(...arguments);
    /**
     * Set this to a value to apply a custom `decimalSeparator` to the instance of `NumberFormat`.
     * This overrides the localization specific separator and the static `NumberFormat.decimalSeparator`.
     *
     * Example: set decimal separator on instance
     * ```
     * const fmt = new NumberFormat({
     *     template         : '',
     *     decimalSeparator : '-'
     * });
     * fmt.format(12345.54321);
     *
     * // Result: '12,345-5432'
     * ```
     * @config {String} decimalSeparator
     * @advanced
     */
    __publicField(this, "decimalSeparator");
    /**
     * Set this to a value to apply a custom `groupSeparator` to the instance of `NumberFormat`.
     * This overrides the localization specific separator and the static `NumberFormat.groupSeparator`.
     *
     * Example: set group separator on instance
     * ```
     * const fmt = new NumberFormat({
     *     template       : '',
     *     groupSeparator : ' '
     * });
     * fmt.format(12345.54321);
     *
     * // Result: '12 345.5432'
     * ```
     * @config {String} groupSeparator
     * @advanced
     */
    __publicField(this, "groupSeparator");
  }
  initialize() {
    this._as = {
      // cacheKey : cachedInstance
    };
    this.is = {
      decimal: false,
      currency: false,
      percent: false,
      null: true,
      from: null
    };
  }
  get truncator() {
    const scale = this.maximumFractionDigits + (this.is.percent ? 2 : 1), digits = Math.min(20, scale);
    return this.as({ style: "decimal", maximumFractionDigits: digits, minimumFractionDigits: digits }, "truncator");
  }
  configure(options) {
    var _a5, _b2;
    const { decimalSeparator, groupSeparator } = _NumberFormat;
    Object.assign(this, {
      ...decimalSeparator ? { decimalSeparator } : {},
      ...groupSeparator ? { groupSeparator } : {}
    });
    if (typeof options !== "string") {
      Object.assign(this, options);
    } else {
      this.template = options;
    }
    const me = this, config = {}, loc = me.locale ? LocaleManager_default.locales[me.locale] || Object.values(LocaleManager_default.locales).find((locale6) => locale6.localeCode === me.locale) : LocaleManager_default.locale, localeDefaults = loc == null ? void 0 : loc.NumberFormat, { template } = me, { standardOptions } = me.constructor;
    if (localeDefaults) {
      for (const key in localeDefaults) {
        if (me[key] == null && typeof localeDefaults[key] !== "function") {
          me[key] = localeDefaults[key];
        }
      }
    }
    if (template) {
      const match = numFormatRe.exec(template), m2 = match[2], m4 = match[4];
      me.useGrouping = Boolean(match[3]);
      me.style = match[1] ? "currency" : match[6] ? "percent" : "decimal";
      if (m2) {
        me.integer = +m2;
      }
      if (m4 === "*") {
        me.fraction = [0, 20];
      } else if (m4 != null) {
        me.fraction = [match[5].length, m4.length];
      }
    }
    me._minMax("fraction", true, true);
    me._minMax("integer", true, false);
    me._minMax("significant", false, true);
    for (const key of standardOptions) {
      if (me[key] != null) {
        config[key] = me[key];
      }
    }
    me.is.from = me.from && me.from.is;
    me.is[me.style] = !(me.is.null = false);
    me.formatter = newFormatter(me.locale, config);
    if (me.decimalSeparator || me.groupSeparator) {
      const { groupSeparator: groupSeparator2, decimalSeparator: decimalSeparator2 } = me.getLocaleSeparators();
      me.separatorRegex = new RegExp(`[${groupSeparator2}${decimalSeparator2}]`, "g");
      me.separatorReplace = {
        [groupSeparator2]: (_a5 = me.groupSeparator) != null ? _a5 : groupSeparator2,
        [decimalSeparator2]: (_b2 = me.decimalSeparator) != null ? _b2 : decimalSeparator2
      };
    }
  }
  /**
   * Creates a derived `NumberFormat` from this instance, with a different `style`. This is useful for processing
   * currency and percentage styles without the symbols being injected in the formatting.
   *
   * @param {String|Object} change The new style (if a string) or a set of properties to update.
   * @param {String} [cacheAs] A key by which to cache this derived formatter.
   * @returns {Core.helper.util.NumberFormat}
   */
  as(change, cacheAs = null) {
    const config = this.resolvedOptions() || { template: "9.*" }, cache = this._as;
    let ret = cacheAs && cache[cacheAs];
    if (!ret) {
      if (typeof change === "string") {
        config.style = change;
      } else {
        Object.assign(config, change);
      }
      config.from = this;
      ret = new _NumberFormat(config);
    }
    if (cacheAs) {
      cache[cacheAs] = ret;
    }
    return ret;
  }
  defaultParse(value2, strict) {
    return value2 == null ? value2 : strict ? Number(value2) : parseFloat(value2);
  }
  getLocaleSeparators() {
    const parts = this.formatter.formatToParts(123456789e-2);
    let decimalSeparator = ".", groupSeparator = ",";
    for (const part of parts) {
      if (part.type === "decimal") {
        decimalSeparator = part.value;
      }
      if (part.type === "group") {
        groupSeparator = part.value;
      }
    }
    return { decimalSeparator, groupSeparator };
  }
  /**
   * Returns the given `value` formatted in accordance with the specified locale and
   * formatting options.
   *
   * @param {Number} value
   * @returns {String}
   */
  format(value2) {
    const me = this, { separatorRegex, separatorReplace } = me;
    if (typeof value2 === "string") {
      const v = Number(value2);
      value2 = isNaN(v) ? me.parse(value2) : v;
    }
    value2 = super.format(value2);
    if (separatorRegex) {
      value2 = String(value2).replace(separatorRegex, (match) => separatorReplace[match]);
    }
    return value2;
  }
  // The parse() method is inherited but the base class implementation
  // cannot properly document the parameter and return types:
  /**
   * Returns a `Number` parsed from the given, formatted `value`, in accordance with the
   * specified locale and formatting options.
   *
   * If the `value` cannot be parsed, `NaN` is returned.
   *
   * Pass `strict` as `true` to require all text to convert. In essence, the default is
   * in line with JavaScript's `parseFloat` while `strict=true` behaves like the `Number`
   * constructor:
   *
   * ```javascript
   *  parseFloat('1.2xx');  // = 1.2
   *  Number('1.2xx')       // = NaN
   * ```
   *
   * @method parse
   * @param {String} value
   * @param {Boolean} [strict=false]
   * @returns {Number}
   */
  /**
   * Returns a `Number` parsed from the given, formatted `value`, in accordance with the
   * specified locale and formatting options.
   *
   * If the `value` cannot be parsed, `NaN` is returned.
   *
   * This method simply passes the `value` to `parse()` and passes `true` for the second
   * argument.
   *
   * @method parseStrict
   * @param {String} value
   * @returns {Number}
   */
  /**
   * Returns the given `Number` rounded in accordance with the specified locale and
   * formatting options.
   *
   * @param {Number|String} value
   * @returns {Number}
   */
  round(value2) {
    return this.parse(this.format(value2));
  }
  /**
   * Returns the given `Number` truncated to the `maximumFractionDigits` in accordance
   * with the specified locale and formatting options.
   *
   * @param {Number|String} value
   * @returns {Number}
   */
  truncate(value2) {
    const me = this, scale = me.maximumFractionDigits + (me.is.percent ? 2 : 0), { truncator } = me;
    let v = me.parse(value2), dot;
    if (truncator) {
      v = truncator.format(v);
      dot = v.indexOf(truncator.parser.decimal);
      if (dot > -1 && v.length - dot - 1 > scale) {
        v = v.slice(0, dot + scale + 1);
      }
      v = truncator.parse(v);
    }
    return v;
  }
  resolvedOptions() {
    const options = super.resolvedOptions();
    for (const key in options) {
      if (options[key] === void 0) {
        options[key] = this[key];
      }
    }
    return options;
  }
  /**
   * Expands the provided shorthand into the "minimum*Digits" and "maximum*Digits".
   * @param {String} name
   * @param {Boolean} setMin
   * @param {Boolean} setMax
   * @private
   */
  _minMax(name, setMin, setMax) {
    const me = this, value2 = me[name];
    if (value2 != null) {
      const capName = StringHelper.capitalize(name), max3 = `maximum${capName}Digits`, min4 = `minimum${capName}Digits`;
      if (typeof value2 === "number") {
        if (setMin) {
          me[min4] = value2;
        }
        if (setMax) {
          me[max3] = value2;
        }
      } else {
        me[min4] = value2[0];
        me[max3] = value2[1];
      }
    }
  }
};
__publicField(_NumberFormat, "$name", "NumberFormat");
/**
 * Set this to a value to apply a default custom `decimalSeparator` to all formatted numbers in the application.
 * This overrides the localization specific separator.
 *
 * Example: set global decimal separator
 * ```javascript
 * // App init code
 * NumberFormat.decimalSeparator = '-';
 *
 * // All numbers displayed in the application will have the custom decimal separator applied:
 * const fmt = new NumberFormat('9,9.99##');
 * fmt.format(12345.54321);
 *
 * // Result: '12,345-5432'
 * ```
 * @static
 * @member {String} decimalSeparator
 * @advanced
 */
__publicField(_NumberFormat, "decimalSeparator");
/**
 * Set this to a value to apply a default custom `groupSeparator` to all formatted numbers in the application.
 * This overrides the localization specific separator.
 *
 * Example: set global group separator
 * ```javascript
 * // App init code
 * NumberFormat.groupSeparator = ' ';
 *
 * // All numbers displayed in the application will have the custom decimal separator applied:
 * const fmt = new NumberFormat('9,9.99##');
 * fmt.format(12345.54321);
 *
 * // Result: '12 345.5432'
 * ```
 * @static
 * @member {String} groupSeparator
 * @advanced
 */
__publicField(_NumberFormat, "groupSeparator");
// This object holds only those properties that Intl.NumberFormat accepts in its
// "options" parameter. Only these options will be copied from the NumberFormat
// and passed to the Intl.NumberFormat constructor and only these will be copied
// back from its resolvedOptions:
__publicField(_NumberFormat, "standardOptions", [
  "currency",
  "currencyDisplay",
  "locale",
  "maximumFractionDigits",
  "minimumFractionDigits",
  "minimumIntegerDigits",
  "maximumSignificantDigits",
  "minimumSignificantDigits",
  "style",
  "useGrouping"
]);
var NumberFormat = _NumberFormat;
NumberFormat.Parser = NumberParser;
Object.assign(NumberFormat.prototype, {
  /**
   * The currency to use when using `style: 'currency'`. For example, `'USD'` (US dollar)
   * or `'EUR'` for the euro.
   *
   * If not provided, the {@link Core.localization.LocaleManager} default will be used.
   * @config {String}
   */
  currency: null,
  /**
   * The format in which to display the currency value when using `style: 'currency'`.
   *
   * Valid values are: `'symbol'` (the default), `'code'`, and `'name'`.
   * @config {'symbol'|'code'|'name'}
   * @default
   */
  currencyDisplay: "symbol",
  /**
   * Specifies the `minimumFractionDigits` and `maximumFractionDigits` in a compact
   * way. If this value is a `Number`, it sets both the minimum and maximum to that
   * value. If this value is an array, `[0]` sets the minimum and `[1]` sets the
   * maximum.
   * @config {Number|Number[]}
   */
  fraction: null,
  from: null,
  /**
   * An alias for `minimumIntegerDigits`.
   * @config {Number}
   */
  integer: null,
  /**
   * The name of the locale. For example, `'en-US'`. This config is the same as the
   * first argument to the `Intl.NumberFormat` constructor.
   *
   * Defaults to the browser's default locale.
   * @config {String}
   */
  locale: null,
  /**
   * The maximum number of digits following the decimal.
   *
   * This is more convenient to specify using the {@link #config-fraction} config.
   * @config {Number}
   */
  maximumFractionDigits: null,
  /**
   * The minimum number of digits following the decimal.
   *
   * This is more convenient to specify using the {@link #config-fraction} config.
   * @config {Number}
   */
  minimumFractionDigits: null,
  /**
   * The minimum number of digits preceding the decimal.
   *
   * This is more convenient to specify using the {@link #config-integer} config.
   * @config {Number}
   */
  minimumIntegerDigits: null,
  /**
   * The maximum number of significant digits.
   *
   * This is more convenient to specify using the {@link #config-significant} config.
   * @config {Number}
   */
  maximumSignificantDigits: null,
  /**
   * The minimum number of significant digits.
   *
   * This is more convenient to specify using the {@link #config-significant} config.
   * @config {Number}
   */
  minimumSignificantDigits: null,
  /**
   * Specifies the `minimumSignificantDigits` and `maximumSignificantDigits` in a compact
   * format. If this value is a `Number`, it sets only the maximum to that value. If this
   * value is an array, `[0]` sets the minimum and `[1]` sets the maximum.
   *
   * If this value (or `minimumSignificantDigits` or `minimumSignificantDigits`) is set,
   * `integer` (and `minimumIntegerDigits`) and `fraction` (and `minimumFractionDigits`
   * and `minimumFractionDigits`) are ignored.
   *
   * @config {Number|Number[]}
   */
  significant: null,
  /**
   * The formatting style.
   *
   * Valid values are: `'decimal'` (the default), `'currency'`, and `'percent'`.
   * @config {'decimal'|'currency'|'percent'}
   * @default
   */
  style: "decimal",
  /**
   * A format template consisting of the following parts:
   *
   * ```text
   *  [$] [\d+:] \d+ [,\d+] [.\d* [#*] | *] [%]
   * ```
   *
   * If the template begins with a `'$'`, the formatter's `style` option is set to
   * `'currency'`. If the template ends with `'%'`, `style` is set to `'percent'`.
   * It is invalid to include both characters. When using `'$'`, the `currency` symbol
   * defaults to what is provided by the {@link Core.localization.LocaleManager}.
   *
   * To set the `minimumIntegerDigits`, the desired minimum comes before the first
   * digits in the template and is followed by a `'>'` (greater-than). For example:
   *
   * ```text
   *  5>9,999.00
   * ```
   *
   * The above sets `minimumIntegerDigits` to 5.
   *
   * The `useGrouping` option is enabled if there is a `','` (comma) present and is
   * disabled otherwise.
   *
   * If there is a `'.'` (decimal) present, it may be followed by either of:
   *
   *  - Zero or more digits which may then be followed by zero or more `'#'` characters.
   *    The number of digits determines the `minimumFractionDigits`, while the total
   *    number of digits and `'#'`s determines the `maximumFractionDigits`.
   *  - A single `'*'` (asterisk) indicating any number of fractional digits (no minimum
   *    or maximum).
   *
   * @config {String}
   */
  template: null,
  /**
   * Specify `false` to disable thousands separators.
   * @config {Boolean}
   * @default
   */
  useGrouping: true
});
Formatter.number = (format2, value2) => NumberFormat.get(format2).format(value2);
LocaleManager_default.ion({
  locale: () => NumberFormat.cache.clear()
});
NumberFormat._$name = "NumberFormat";

// ../Core/lib/Core/data/Duration.js
var Duration = class _Duration {
  /**
   * Duration constructor.
   * @function constructor
   * @param {Number|String} magnitude Duration magnitude value or a duration + magnitude string ('2h', '4d')
   * @param {DurationUnit} [unit] Duration unit value
   * @category Lifecycle
   */
  constructor(magnitude, unit) {
    if (typeof magnitude === "number" || magnitude === null) {
      this._magnitude = magnitude;
      this._unit = unit;
    } else {
      if (typeof magnitude === "string") {
        Object.assign(this, DateHelper.parseDuration(magnitude));
      }
      if (typeof magnitude === "object") {
        this.magnitude = magnitude.magnitude;
        this.unit = magnitude.unit;
      }
    }
  }
  /**
   * Converts the passed parameter converted to a {@link Core.data.Duration} object if possible,
   * or `null` if `null` or `undefined` passed.
   * @param {Number|String|Object|Core.data.Duration} source The data to be converted into a Duration.
   * @returns {Core.data.Duration} The passed parameter converted to a {@link Core.data.Duration} object if possible,
   * or `null` if `null` or `undefined` passed.
   */
  static from(source) {
    return source == null ? null : source.isDuration ? source : new _Duration(source);
  }
  /**
   * Returns a negated copy of this Duration.
   * @returns {Core.data.Duration} a negated copy of this Duration.
   */
  negate() {
    return new _Duration(-this.magnitude, this.unit);
  }
  /**
   * Get/Set numeric magnitude `value`.
   * @property {Number}
   */
  get magnitude() {
    return this._magnitude;
  }
  set magnitude(value2) {
    this._magnitude = typeof value2 === "number" && value2;
  }
  /**
   * Get/set duration unit to use with the current magnitude value.
   * Valid values are:
   * - "millisecond" - Milliseconds
   * - "second" - Seconds
   * - "minute" - Minutes
   * - "hour" - Hours
   * - "day" - Days
   * - "week" - Weeks
   * - "month" - Months
   * - "quarter" - Quarters
   * - "year"- Years
   *
   * @member {DurationUnit}
   */
  get unit() {
    return this._unit;
  }
  set unit(value2) {
    this._unit = DateHelper.parseTimeUnit(value2);
  }
  get isValid() {
    return this._magnitude != null && Boolean(DateHelper.normalizeUnit(this._unit));
  }
  /**
   * The `milliseconds` property is a read only property which returns the number of milliseconds in this Duration
   * @property {Number}
   * @readonly
   */
  get milliseconds() {
    return this.isValid ? Math.round(DateHelper.asMilliseconds(this._magnitude, this._unit)) : 0;
  }
  /**
   * Returns truthy value if this Duration equals the passed value.
   * @param {Core.data.Duration} value
   * @returns {Boolean}
   */
  isEqual(value2) {
    return Boolean(value2) && this._magnitude != null && value2._magnitude != null && this.milliseconds === value2.milliseconds;
  }
  /**
   * Returns a readable localized representation of this Duration (e.g. 5 days).
   * @param {Boolean} [useAbbreviation=false] Pass `true` to return an abbreviated version (e.g. 5d)
   * @param {Boolean} [nbrDecimals=10] The maximum number of decimals for comparison and displaying in the editor
   * @returns {String}
   */
  toString(useAbbreviation, nbrDecimals = 10) {
    const me = this, abbreviationFn = useAbbreviation ? "getShortNameOfUnit" : "getLocalizedNameOfUnit", space = useAbbreviation ? "" : " ";
    return me.isValid ? `${NumberFormat.get("9." + "#".repeat(nbrDecimals)).format(me._magnitude)}${space}${DateHelper[abbreviationFn](me._unit, Math.abs(me._magnitude) !== 1)}` : "";
  }
  toJSON() {
    return this.toString();
  }
  valueOf() {
    return this.milliseconds;
  }
  add(otherDuration) {
    return new _Duration({
      unit: this.unit,
      magnitude: DateHelper.as(this.unit, this.milliseconds + new _Duration({ magnitude: otherDuration.magnitude, unit: otherDuration.unit || this.unit }).milliseconds)
    });
  }
  diff(otherDuration) {
    return new _Duration({
      unit: this.unit,
      magnitude: DateHelper.as(this.unit, this.milliseconds - otherDuration.milliseconds)
    });
  }
};
Duration._$name = "Duration";

// ../Core/lib/Core/util/CollectionFilter.js
var nestedValueReducer = (object, path) => object == null ? void 0 : object[path];
var relativeDateUnitRegExp = /^is(this|next|last)(week|month|year)$/i;
var relativeDateOperators = [
  "isToday",
  "isTomorrow",
  "isYesterday",
  "isThisWeek",
  "isNextWeek",
  "isLastWeek",
  "isThisMonth",
  "isNextMonth",
  "isLastMonth",
  "isThisYear",
  "isNextYear",
  "isLastYear",
  "isYearToDate"
];
var _CollectionFilter = class _CollectionFilter extends Base.mixin(Identifiable_default) {
  construct(config) {
    if (typeof config === "function") {
      config = {
        filterBy: config
      };
    }
    if (!config.type) {
      if (DateHelper.isDate(config.value) || Array.isArray(config.value) && config.value.every(DateHelper.isDate)) {
        config.type = "date";
      } else if (config.value instanceof Duration) {
        config.type = "duration";
      }
    } else {
      if (config.type === "date" && config.value != null) {
        if (Array.isArray(config.value)) {
          config.value = config.value.map((val) => val instanceof Date ? val : new Date(val));
        } else {
          config.value = new Date(config.value);
        }
      } else if (config.type === "duration" && config.value != null) {
        if (Array.isArray(config.value)) {
          config.value = config.value.map((val) => val.isDuration ? val : new Duration(val));
        } else {
          config.value = new Duration(config.value);
        }
      }
    }
    super.construct(config);
  }
  /**
   * When in a Collection (A Collection holds its Filters in a Collection), we need an id.
   * @property {String}
   * @private
   */
  get id() {
    if (!this._id) {
      if (this.internal) {
        this._id = _CollectionFilter.generateId(`b-internal-${this.property}-filter-`);
      } else {
        this._id = this.property && `${this.property}-${this.operator}` || _CollectionFilter.generateId("b-filter-");
      }
    }
    return this._id;
  }
  set id(id2) {
    this._id = id2;
  }
  onChange(propertyChanged) {
    var _a5;
    const me = this;
    if (!me.isConfiguring && ((_a5 = me.owner) == null ? void 0 : _a5.onFilterChanged) && !me.owner.isConfiguring) {
      me.owner.onFilterChanged(me, propertyChanged);
    }
  }
  /**
   * May be used in place of the {@link #config-property}, {@link #config-value} and {@link #config-property} configs.
   * A function which accepts a candidate object and returns `true` or `false`
   * @member {Function} filterBy
   * @param {*} data Data object for filtering
   * @returns {Boolean} Returns `true` for data valid for including
   */
  get filterBy() {
    return this._filterBy || this.defaultFilterBy;
  }
  defaultFilterBy(candidate) {
    const me = this;
    let candidateValue;
    if (candidate.isModel) {
      candidateValue = candidate.getValue(me.property);
    } else if (me._propertyItems.length > 1) {
      candidateValue = me._propertyItems.reduce(nestedValueReducer, candidate);
    } else {
      candidateValue = candidate[me.property];
    }
    return me[me.operator](me.convert(candidateValue));
  }
  updateProperty(property) {
    this._propertyItems = property.split(".");
    this.onChange("property");
  }
  /**
   * The value against which to compare the {@link #config-property} of candidate objects.
   * @member {*} value
   */
  updateValue(value2) {
    const me = this;
    if (Array.isArray(value2) && ({ date: 1, duration: 1 }[me.type] || value2.length > 0 && typeof value2[0] === "string")) {
      me._filterValue = value2.map((v) => me.convert(v));
    } else if (!me.caseSensitive && Array.isArray(value2) && value2.length > 0 && typeof value2[0] === "string") {
      me._filterValue = value2.map((s) => s == null ? void 0 : s.toLowerCase());
    } else if (!me.caseSensitive && typeof value2 === "string") {
      me._filterValue = value2.toLowerCase();
    } else {
      me._filterValue = me.convert(value2);
    }
    me.updateInternalFilterValue();
    me.onChange("value");
  }
  get filterValue() {
    return this._filterValue;
  }
  updateInternalFilterValue() {
    const me = this, { value: value2 } = me;
    if (Array.isArray(value2) && ({ date: 1, duration: 1 }[me.type] || value2.length > 0 && typeof value2[0] === "string")) {
      me._filterValue = value2.map((v) => me.convert(v));
    } else if (!me._caseSensitive && Array.isArray(value2) && value2.length > 0 && typeof value2[0] === "string") {
      me._filterValue = value2.map((s) => s == null ? void 0 : s.toLowerCase());
    } else if (!me._caseSensitive && typeof value2 === "string") {
      me._filterValue = value2.toLowerCase();
    } else {
      me._filterValue = me.convert(value2);
    }
  }
  /**
   * The operator to use when comparing a candidate object's {@link #config-property} with this CollectionFilter's
   * {@link #config-value}.
   * @member {CollectionCompareOperator} operator
   */
  updateOperator() {
    this.onChange("operator");
  }
  get operator() {
    const me = this;
    if (me._operator) {
      return me._operator;
    }
    if (Array.isArray(me.filterValue)) {
      return "isIncludedIn";
    }
    return typeof me.filterValue === "string" ? "*" : "=";
  }
  updateCaseSensitive() {
    this.updateInternalFilterValue();
  }
  convert(value2) {
    var _a5;
    const me = this;
    if (me.operator !== "sameTime" && me.operator !== "notSameTime" && !(typeof me.filterValue === "string" && value2 instanceof Date)) {
      if (me.operator === "sameDay" || me.operator === "notSameDay") {
        value2 = DateHelper.clearTime(value2);
      }
      value2 = (_a5 = value2 == null ? void 0 : value2.valueOf()) != null ? _a5 : value2;
    }
    value2 = !me.caseSensitive && typeof value2 === "string" ? value2.toLowerCase() : value2;
    return value2;
  }
  filter(candidate) {
    return this.filterBy(candidate);
  }
  startsWith(v) {
    return String(v).startsWith(this.filterValue);
  }
  endsWith(v) {
    return String(v).endsWith(this.filterValue);
  }
  isIncludedIn(v) {
    return this.filterValue.length === 0 || this.filterValue.includes(v);
  }
  isNotIncludedIn(v) {
    return !this.isIncludedIn(v);
  }
  includes(v) {
    return this.filterValue.length === 0 || String(v).includes(this.filterValue);
  }
  doesNotInclude(v) {
    return !this.includes(v);
  }
  sameTime(v) {
    return v != null && DateHelper.isSameTime(v, this.filterValue);
  }
  notSameTime(v) {
    return !this.sameTime(v);
  }
  sameDay(v) {
    return v === this.filterValue;
  }
  notSameDay(v) {
    return v !== this.filterValue;
  }
  "="(v) {
    return ObjectHelper.isEqual(v, this.filterValue);
  }
  "!="(v) {
    return !ObjectHelper.isEqual(v, this.filterValue);
  }
  ">"(v) {
    return ObjectHelper.isMoreThan(v, this.filterValue);
  }
  ">="(v) {
    return ObjectHelper.isMoreThan(v, this.filterValue) || ObjectHelper.isEqual(v, this.filterValue);
  }
  "<"(v) {
    return ObjectHelper.isLessThan(v, this.filterValue);
  }
  "<="(v) {
    return ObjectHelper.isLessThan(v, this.filterValue) || ObjectHelper.isEqual(v, this.filterValue);
  }
  "*"(v) {
    return ObjectHelper.isPartial(v, this.filterValue);
  }
  between(v) {
    const [start, end] = this._filterValue;
    return (ObjectHelper.isMoreThan(v, start) || ObjectHelper.isEqual(v, start)) && (ObjectHelper.isLessThan(v, end) || ObjectHelper.isEqual(v, end));
  }
  notBetween(v) {
    return !this.between(v);
  }
  betweenLesser(v) {
    const [start, end] = this._filterValue;
    return v >= start && v < end;
  }
  empty(v) {
    return v === void 0 || v === null || String(v).length === 0;
  }
  notEmpty(v) {
    return !this.empty(v);
  }
  isToday(v) {
    return this.betweenLesser(v);
  }
  isTomorrow(v) {
    return this.betweenLesser(v);
  }
  isYesterday(v) {
    return this.betweenLesser(v);
  }
  isThisWeek(v) {
    return this.betweenLesser(v);
  }
  isNextWeek(v) {
    return this.betweenLesser(v);
  }
  isLastWeek(v) {
    return this.betweenLesser(v);
  }
  isThisMonth(v) {
    return this.betweenLesser(v);
  }
  isNextMonth(v) {
    return this.betweenLesser(v);
  }
  isLastMonth(v) {
    return this.betweenLesser(v);
  }
  isThisYear(v) {
    return this.betweenLesser(v);
  }
  isNextYear(v) {
    return this.betweenLesser(v);
  }
  isLastYear(v) {
    return this.betweenLesser(v);
  }
  isYearToDate(v) {
    return this.between(v);
  }
  isTrue(v) {
    return v === true;
  }
  isFalse(v) {
    return v === false;
  }
  // Get all relevant fields to save in the store's filter state
  getState() {
    const {
      caseSensitive,
      disabled,
      id: id2,
      internal,
      operator,
      property,
      type,
      value: value2,
      columnOwned,
      filterBy
    } = this;
    const result = {
      caseSensitive,
      disabled,
      id: id2,
      internal,
      operator,
      property,
      type,
      value: value2,
      columnOwned,
      filterBy
    };
    ObjectHelper.cleanupProperties(result, true);
    if (result.filterBy === this.defaultFilterBy) {
      delete result.filterBy;
    }
    return result;
  }
  // Fill in actual dates relative to now
  setRelativeDateValues() {
    this._filterValue = _CollectionFilter.getRelativeDateRange(this._operator).map((date2) => date2.valueOf());
  }
  // Indicate when our internal logic results in this filter having no effect
  get isNoOp() {
    var _a5;
    return (this.operator === "includes" || this.operator === "isIncludedIn") && ((_a5 = this.filterValue) == null ? void 0 : _a5.length) === 0;
  }
  static getRelativeDateRange(relativeExpr, now2 = /* @__PURE__ */ new Date()) {
    let todayStart, tomorrowStart, parts, oneTimeUnit, unitStart, which, timeUnit;
    switch (relativeExpr) {
      case "isYearToDate":
        return [DateHelper.floor(now2, "1 year"), now2];
      case "isToday":
        todayStart = DateHelper.floor(now2, "1 day");
        return [todayStart, DateHelper.add(todayStart, 1, "day")];
      case "isYesterday":
        todayStart = DateHelper.floor(now2, "1 day");
        return [DateHelper.add(todayStart, -1, "day"), todayStart];
      case "isTomorrow":
        tomorrowStart = DateHelper.getStartOfNextDay(now2);
        return [tomorrowStart, DateHelper.add(tomorrowStart, 1, "day")];
      case "isThisWeek":
      case "isNextWeek":
      case "isLastWeek":
      case "isThisMonth":
      case "isNextMonth":
      case "isLastMonth":
      case "isThisYear":
      case "isNextYear":
      case "isLastYear":
        parts = relativeExpr.toLowerCase().match(relativeDateUnitRegExp);
        if (!parts) {
          throw new Error(`Unrecognized relative date expression: ${relativeExpr}`);
        }
        [, which, timeUnit] = parts;
        oneTimeUnit = `1 ${timeUnit}`;
        unitStart = DateHelper.floor(now2, oneTimeUnit);
        if (which === "next") {
          unitStart = DateHelper.add(unitStart, 1, timeUnit);
        } else if (which === "last") {
          unitStart = DateHelper.add(unitStart, -1, timeUnit);
        }
        return [unitStart, DateHelper.add(unitStart, 1, timeUnit)];
    }
  }
  /**
   * Generates a filter function based on provided collection filters.
   * @advanced
   * @param {Core.util.CollectionFilter[]|Core.util.Collection} filters Filters used for creating a filtering function
   * @returns {Function} Returns a filter function
   */
  static generateFiltersFunction(filters) {
    if (!filters || !filters.length && !filters.count) {
      return FunctionHelper.returnTrue;
    }
    for (const filter2 of filters) {
      if (filter2.type === "date" && relativeDateOperators.includes(filter2._operator)) {
        filter2.setRelativeDateValues();
      }
    }
    return function(candidate) {
      let match = true;
      for (const filter2 of filters) {
        if (!filter2.disabled) {
          match = filter2.filter(candidate);
        }
        if (!match) {
          break;
        }
      }
      return match;
    };
  }
};
__publicField(_CollectionFilter, "$name", "CollectionFilter");
__publicField(_CollectionFilter, "configurable", {
  /**
   * The value against which to compare the {@link #config-property} of candidate objects.
   * @config {*}
   */
  value: null,
  /**
   * The operator to use when comparing a candidate object's {@link #config-property} with this CollectionFilter's
   * {@link #config-value}.
   * @config {CollectionCompareOperator}
   */
  operator: null,
  /**
   * May be used in place of the {@link #config-property}, {@link #config-value} and {@link #config-property}
   * configs. A function which accepts a candidate object and returns `true` or `false`
   * @config {Function}
   * @param {*} data Data object for filtering
   * @returns {Boolean}
   */
  filterBy: null,
  /**
   * A function which accepts a value extracted from a candidate object using the {@link #config-property} name,
   * and returns the value which the filter should use to compare against its {@link #config-value}.
   * @config {Function} convert
   * @param {*} value
   * @returns {*}
   */
  /**
   * Configure as `false` to have string comparisons case-insensitive.
   * @config {Boolean}
   * @default
   */
  caseSensitive: true,
  /**
   * The `id` of this Filter for when used by a {@link Core.util.Collection} Collection.
   * By default, the `id` is the `{@link #config-property}-{@link #config-operator}` value.
   * @config {String}
   */
  id: null,
  // Type is required to process the Date value in State API. Store doesn't always know about field type to
  // process filter value, when it applies it from the state, e.g. when you don't declare model field as `date`
  // type but provide a Date instance there. When DateColumn is used to shows this field, it could add date
  // filters to the store. When store is applying state it cannot just infer type, because model doesn't
  // declare it. Only column knows. So to properly process the Date instance for the filter State API would
  // have to process the field additionally, checking model field type and column type. So it is simpler to
  // make Filter to put this information. That way when filter is instantiated by the store, it can gracefully
  // handle value processing, converting date string to the Date instance.
  // Date is the only known value type so far which requires this processing.
  type: null,
  /**
   * Setting the `internal` config on a filter means that it is a fixed part of your store's operation.
   *
   * {@link Core.data.Store#function-clearFilters} does not remove `internal` filters. If you add an
   * `internal` filter, you must explicitly remove it if it is no longer required.
   *
   * Grid features which offer column-based filtering do *not* ingest existing store filters on
   * their data field if the filter is `internal`
   * @config {Boolean}
   * @default false
   */
  internal: null,
  /**
   * When `true`, the filter will not be applied.
   * @config {Boolean}
   * @default
   */
  disabled: false,
  /**
   * The name of a property of candidate objects which yields the value to compare against this CollectionFilter's
   * {@link #config-value}.
   * @member {String} property
   */
  /**
   * The name of a property of candidate objects which yields the value to compare against this CollectionFilter's
   * {@link #config-value}.
   * @config {String}
   */
  property: null
});
var CollectionFilter = _CollectionFilter;
CollectionFilter._$name = "CollectionFilter";

// ../Core/lib/Core/helper/IdHelper.js
var idCounts2 = ObjectHelper.getPathDefault(globalThis, "bryntum.idCounts", /* @__PURE__ */ Object.create(null));
var IdHelper = class {
  /**
   * Generate a new id, using IdHelpers internal counter and a prefix
   * @param {String} prefix Id prefix
   * @returns {String} Generated id
   */
  static generateId(prefix = "generatedId") {
    return prefix + (idCounts2[prefix] = (idCounts2[prefix] || 0) + 1);
  }
  static reset(prefix) {
    idCounts2[prefix] = 0;
  }
};
IdHelper._$name = "IdHelper";

// ../Core/lib/Core/util/CollectionSorter.js
var CollectionSorter = class extends Base {
  static get defaultConfig() {
    return {
      /**
       * The name of a property of collection objects which yields the value to sort by.
       * @config {String}
       */
      property: null,
      /**
       * Configure as `false` to have string comparisons case-insensitive.
       * @config {Boolean}
       * @default
       */
      caseSensitive: true,
      /**
       * The direction to sort in, `'ASC'` or `'DESC'`
       * @config {'ASC'|'DESC'}
       * @default
       */
      direction: "ASC",
      /**
       * A function which takes the place of using {@link #config-property} and {@link #config-direction}.
       * The function is passed two objects from the collection to compare and must return the comparison result.
       * @config {Function}
       * @param {*} first The first value to compare
       * @param {*} second The second value to compare
       * @param {'ASC'|'DESC'} direction The sort direction
       * @returns {Number}  Returns `1` if first value is greater than second value, `-1` if the opposite is true
       * or `0` if they're equal
       */
      sortFn: null,
      /**
       * When using {@link #config-property}, this may be specified as a function which takes the raw
       * property value and returns the value to actually sort by.
       * @config {Function}
       * @param {*} value
       * @returns {*}
       */
      convert: null,
      /**
       * The `id` of this Sorter for when used by a {@link Core.util.Collection} Collection.
       * By default the `id` is the {@link #config-property} value.
       * @config {String}
       */
      id: null,
      /**
       * Use `localeCompare()` when sorting, which lets the browser sort in a locale specific order. Set to `true`,
       * a locale string or a locale config to enable.
       *
       * Enabling this has big negative impact on sorting performance. For more info on `localeCompare()`, see
       * [MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/localeCompare).
       *
       * ```javascript
       * collection.addSorter({ property : 'name', useLocaleSort : 'sv-SE' });
       * ```
       *
       * @config {Boolean|String|Object}
       * @default false
       */
      useLocaleSort: null
    };
  }
  construct(config) {
    if (typeof config === "function") {
      config = {
        sortFn: config
      };
    }
    super.construct(config);
  }
  /**
   * When in a Collection (A Collection holds its Sorters in a Collection), we need an id.
   * @property {String}
   * @private
   */
  get id() {
    return this._id || (this._id = this.property || IdHelper.generateId("b-sorter"));
  }
  set id(id2) {
    this._id = id2;
  }
  set sortFn(sortFn) {
    this._sortFn = sortFn;
  }
  get sortFn() {
    if (this._sortFn) {
      return this._sortFn;
    }
    return this.defaultSortFn;
  }
  /**
   * Default sortFn used when no sortFn specified. Uses the {@link #config-property},
   * {@link #config-direction}, and {@link #config-convert}.
   * @private
  */
  defaultSortFn(lhs, rhs) {
    const me = this, { convert, property, useLocaleSort, caseSensitive } = me, multiplier = me.direction.toLowerCase() === "desc" ? -1 : 1;
    lhs = lhs[property];
    rhs = rhs[property];
    if (convert) {
      lhs = convert(lhs);
      rhs = convert(rhs);
    }
    if (!caseSensitive) {
      if (typeof lhs === "string") {
        lhs = lhs.toLowerCase();
      }
      if (typeof rhs === "string") {
        rhs = rhs.toLowerCase();
      }
    }
    if (useLocaleSort && lhs != null && rhs != null && typeof lhs === "string") {
      if (useLocaleSort === true) {
        return lhs.localeCompare(rhs);
      }
      if (typeof useLocaleSort === "string") {
        return lhs.localeCompare(rhs, useLocaleSort);
      }
      if (typeof useLocaleSort === "object") {
        return lhs.localeCompare(rhs, useLocaleSort.locale, useLocaleSort);
      }
    }
    return (lhs > rhs ? 1 : lhs < rhs ? -1 : 0) * multiplier;
  }
  static generateSortFunction(sorters, tieBreaker) {
    const items2 = sorters.isCollection ? sorters.values : sorters, n = items2.length;
    return (lhs, rhs) => {
      let comp, i, sorter;
      for (i = 0; i < n; ++i) {
        sorter = items2[i];
        comp = sorter.sortFn(lhs, rhs, sorter.direction);
        if (comp) {
          return comp;
        }
      }
      return tieBreaker ? tieBreaker(lhs, rhs) : 0;
    };
  }
};
CollectionSorter._$name = "CollectionSorter";

// ../Core/lib/Core/util/Collection.js
var return0 = () => 0;
var reverseNumericSortFn = (a, b) => b - a;
var filteredIndicesProperty = Symbol("filteredIndicesProperty");
var emptyArray3 = Object.freeze([]);
var sortEvent = Object.freeze({
  action: "sort",
  added: emptyArray3,
  removed: emptyArray3,
  replaced: emptyArray3
});
var filterEvent = Object.freeze({
  action: "filter",
  added: emptyArray3,
  removed: emptyArray3,
  replaced: emptyArray3
});
var keyTypes = {
  string: 1,
  number: 1
};
function addItemToIndex(item, index, key, detectDuplicates) {
  if (index.unique !== false) {
    index.set(key, item);
    if (detectDuplicates) {
      index.count++;
    }
  } else {
    let set = index.get(key);
    if (!set) {
      set = /* @__PURE__ */ new Set();
      index.set(key, set);
    }
    set.add(item);
  }
}
function removeItemFromIndex(item, index, key) {
  if (index.unique !== false) {
    index.delete(key);
  } else if (index.has(key)) {
    index.get(key).delete(item);
    if (!index.get(key).size) {
      index.delete(key);
    }
  }
}
function doRebuildIndices(values, indices, keyProps, indexCount, ignoreDetectDuplicates = false) {
  for (let i = 0; i < values.length; i++) {
    const item = values[i];
    if (item) {
      for (let j = 0; j < indexCount; j++) {
        const keyProp = keyProps[j], key = item[keyProp], index = indices[keyProp];
        key !== void 0 && addItemToIndex(item, index, key, !ignoreDetectDuplicates && index.onDuplicate);
      }
    }
  }
}
var Collection = class _Collection extends Base.mixin(Events_default) {
  constructor() {
    super(...arguments);
    __publicField(this, "_sortFunction", null);
    __publicField(this, "_addedValues", null);
  }
  //region Config
  static get configurable() {
    return {
      /**
       * Specify the name of the property of added objects which provides the lookup key
       * @config {String}
       * @default
       */
      idProperty: "id",
      /**
       * Specify the names or index configs of properties which are to be indexed for fast lookup.
       *
       * Index configs use the format `{ property : string, unique : boolean }`. Unique indices stores one index
       * per entry, non-unique stores a `Set`. If left out, `unique` defaults to `true`
       *
       * @config {String[]|Object[]}
       * @property {String} property Property to index by
       * @property {Boolean} [unique=true] `true` for unique keys (~primary keys), `false` for non-unique keys
       * (~foreign keys)
       */
      extraKeys: null,
      /**
       * Automatically apply filters on item add.
       * @config {Boolean}
       * @default
       */
      autoFilter: true,
      /**
       * A {@link Core.util.CollectionSorter Sorter}, or Sorter config object, or
       * an array of these, to use to sort this Collection.
       * @config {CollectionSorterConfig[]}
       * @default []
       */
      sorters: {
        $config: ["lazy"],
        value: []
      }
    };
  }
  get isCollection() {
    return true;
  }
  //endregion
  //region Init & destroy
  construct(config) {
    this.generation = 0;
    this._values = [];
    super.construct(config);
  }
  doDestroy() {
    var _a5;
    super.doDestroy();
    const me = this;
    me._values.length = 0;
    if (me.isFiltered) {
      me._filteredValues.length = 0;
      me.filters.destroy();
    }
    (_a5 = me._sorters) == null ? void 0 : _a5.destroy();
  }
  //endregion
  //region "CRUD"
  /**
   * Clears this collection.
   */
  clear() {
    const me = this, removed = me._values.filter((rec) => rec);
    if (me.totalCount) {
      me._values.length = 0;
      if (me._filteredValues) {
        me._filteredValues.length = 0;
      }
      me._indicesInvalid = true;
      me.generation++;
      me.trigger("change", {
        action: "clear",
        removed
      });
    }
  }
  /**
   * Compares the content of this Collection with the content of the passed Collection or
   * with the passed array. Order insensitive. This returns `true` if the two objects passed
   * contain the same set of items.
   * @param {Core.util.Collection|Array} other The Collection or array to compare with.
   * @param {Function} [map] Optionally a function to convert the items into a comparable object
   * to compare. For example `item => item.id` could be used to compare the ids of the
   * constituent items.
   * @returns {Boolean} `true` if the two objects passed have the same content.
   */
  equals(other, map2) {
    if (other.isCollection) {
      other = other.values;
    }
    if (other.length === this.count) {
      let { values } = this;
      if (map2) {
        other = other.map(map2);
        values = values.map(map2);
      }
      return ArrayHelper.delta(other, values).inBoth.length === this.count;
    }
    return false;
  }
  /**
   * Replaces the internal `values` array with the passed `values`, or `filteredValues` array with the passed `filteredValues`.
   * If `filteredValues` are not passed explicitly, but storage is filtered, decides internally `values` or `filteredValues` should
   * be replaced by passed `values`.
   *
   * Note that this takes ownership of the array, and the array must not be mutated by outside code.
   *
   * This is an internal utility method, not designed for use by application code.
   *
   * @param {Object} params Values and parameters to replace
   * @param {Object[]} params.values The new `values` array
   * @param {Object[]} [params.filteredValues] The new `filteredValues` array. Applicable only when storage is filtered.
   * @param {Boolean} [params.silent=false] If true, `change` event will not be fired
   * @param {Boolean} [params.isNewDataset=false] If true, `values` is a new dataset
   * @fires change
   * @internal
   */
  replaceValues({ values, filteredValues, silent = false, isNewDataset = false }) {
    const me = this;
    let replacedValues, replacedFilteredValues;
    if (me.isFiltered && !isNewDataset) {
      const filteredPassed = Boolean(filteredValues);
      if (!filteredPassed) {
        filteredValues = values.slice();
        values = null;
      } else if (values) {
        replacedValues = me._values;
        me._values = values.slice();
      }
      replacedFilteredValues = me._filteredValues;
      me._filteredValues = filteredValues.slice();
    } else {
      replacedValues = me._values;
      me._values = values.slice();
      filteredValues = null;
      if (me.isFiltered && isNewDataset && me.autoFilter) {
        me._filterFunction = null;
        me._filteredValues = me._values.filter(me.filterFunction);
      } else if (me._filteredValues) {
        me._filteredValues.length = 0;
      }
    }
    me._indicesInvalid = true;
    me._addedValues = void 0;
    me.generation++;
    if (!silent) {
      me.trigger("change", {
        action: "replaceValues",
        replacedValues,
        replacedFilteredValues,
        values,
        filteredValues
      });
    }
  }
  set values(values) {
    this.invalidateIndices();
    this.splice(0, this._values.length, values);
  }
  /**
   * The set of values of this Collection. If this Collection {@link #property-isFiltered},
   * this yields the filtered data set.
   *
   * Setting this property replaces the data set.
   * @property {Object[]}
   */
  get values() {
    return this.isFiltered ? this._filteredValues : this._values;
  }
  /**
   * The set of filtered values of this Collection (those matching the current filters).
   * @property {Object[]}
   * @private
   */
  get filteredValues() {
    return this._filteredValues;
  }
  /**
   * Iterator that allows you to do `for (const item of collection)`
   */
  [Symbol.iterator]() {
    return this.values[Symbol.iterator]();
  }
  /**
   * Executes the passed function for each item in this Collection, passing in the item,
   * ths index, and the full item array.
   * @param {Function} fn The function to execute.
   * @param {Boolean} [ignoreFilters=false] Pass `true` to include all items, bypassing filters.
   */
  forEach(fn2, ignoreFilters = false) {
    (this.isFiltered && !ignoreFilters ? this._filteredValues : this._values).forEach(fn2);
  }
  /**
   * Extracts ths content of this Collection into an array based upon the passed
   * value extraction function.
   * @param {Function} fn A function, which, when passed an item, returns a value to place into the resulting array.
   * @param {Boolean} [ignoreFilters=false] Pass `true` to process an item even if it is filtered out.
   * @returns {Object[]} An array of values extracted from this Collection.
   */
  map(fn2, ignoreFilters = false) {
    return (this.isFiltered && !ignoreFilters ? this._filteredValues : this._values).map(fn2);
  }
  /**
   * Returns an accumulated value based on the passed function similar to `Array.reduce`.
   * @param {Function} fn A function, which, when passed an accumulator value and an item, returns the new accumulator value.
   * @param {Boolean} [ignoreFilters=false] Pass `true` to process an item even if it is filtered out.
   * @param {*} [accumulator=[]] The initial value of the accumulator.
   * @returns {*} the final accumulator value.
   */
  reduce(fn2, ignoreFilters = false, accumulator = []) {
    return (this.isFiltered && !ignoreFilters ? this._filteredValues : this._values).reduce(fn2, accumulator);
  }
  /**
   * Returns the first item in this Collection which elicits a *truthy* return value from the passed function.
   * @param {Function} fn A function, which, when passed an item, returns `true` to select it as the item to return.
   * @param {Boolean} [ignoreFilters=false] Pass `true` to include filtered out items.
   * @returns {Object} The matched item, or `undefined`.
   */
  find(fn2, ignoreFilters = false) {
    return (this.isFiltered && !ignoreFilters ? this._filteredValues : this._values).find(fn2);
  }
  get first() {
    return this.values[0];
  }
  get last() {
    return this.values[this.count - 1];
  }
  /**
   * The set of all values of this Collection regardless of filters applied.
   * @readonly
   * @property {Object[]}
   */
  get allValues() {
    return this._values;
  }
  /**
   * The set of values added to this Collection since the last sort or replaceValues operation.
   * @private
   * @readonly
   * @property {Object[]}
   */
  get addedValues() {
    return this._addedValues;
  }
  /**
   * This method ensures that every item in this Collection is replaced by the matched by
   * `id` item in the other Collection.
   *
   * By default, any items in this Collection which are __not__ in the other Collection are removed.
   *
   * If the second parameter is passed as `false`, then items which are not in the other
   * Collection are not removed.
   *
   * This can be used for example when updating a selected record Collection when a new
   * Store or new store dataset arrives. The selected Collection must reference the latest
   * versions of the selected record `id`s
   * @param {Core.util.Collection} other The Collection whose items to match.
   */
  match(other, allowRemove = true) {
    const me = this, { _values } = me, toRemove = [];
    me.forEach((item) => {
      const newInstance = other.get(item.id, true);
      if (newInstance) {
        const index = me.indexOf(item, true), oldInstance = _values[index];
        _values[index] = newInstance;
        me.removeFromIndices(oldInstance);
        me.addToIndices(newInstance);
      } else if (allowRemove) {
        toRemove.push(item);
      }
    });
    if (toRemove.length) {
      me.remove(toRemove);
    }
    if (me.isFiltered) {
      me._filteredValues = me._values.filter(me.filterFunction);
    }
    return toRemove;
  }
  /**
   * Adds items to this Collection. Multiple new items may be passed.
   *
   * By default, new items are appended to the existing values.
   *
   * Any {@link #property-sorters} {@link #property-sorters} present are re-run.
   *
   * Any {@link #property-filters} {@link #property-filters} present are re-run.
   *
   * *Note that if application functionality requires add and remove, the
   * {@link #function-splice} operation is preferred as it performs both
   * operations in an atomic manner*
   * @param  {...Object} items The item(s) to add.
   */
  add(...items2) {
    if (items2.length === 1) {
      this.splice(this._values.length, null, ...items2);
    } else {
      this.splice(this._values.length, null, items2);
    }
  }
  /**
   * Removes items from this Collection. Multiple items may be passed.
   *
   * Any {@link #property-sorters} {@link #property-sorters} present are re-run.
   *
   * Any {@link #property-filters} {@link #property-filters} present are re-run.
   *
   * *Note that if application functionality requires add and remove, the
   * {@link #function-splice} operation is preferred as it performs both
   * operations in an atomic manner*
   * @param  {...Object} items The item(s) to remove.
   */
  remove(...items2) {
    if (items2.length === 1) {
      this.splice(0, ...items2);
    } else {
      this.splice(0, items2);
    }
  }
  /**
   * Moves an individual item, or a block of items to another location.
   * @param {Object|Object[]} items The item/items to move.
   * @param {Object} [beforeItem] the item to insert the first item before. If omitted, the `item`
   * is moved to the end of the Collection.
   * @returns {Number} The new index of the `item`.
   */
  move(items2, beforeItem) {
    items2 = ArrayHelper.asArray(items2);
    while (items2.length && items2[0] === beforeItem) {
      items2.shift();
    }
    if (!items2.length) {
      return;
    }
    const me = this, { _values } = me, itemIndex = me.indexOf(items2[0], true);
    if (items2.length === 1 && _values[itemIndex + 1] === beforeItem) {
      return;
    }
    me.suspendEvents();
    me.remove(items2);
    me.resumeEvents();
    const beforeIndex = beforeItem ? me.indexOf(beforeItem, true) : _values.length;
    if (beforeIndex === -1) {
      throw new Error("Collection move beforeItem parameter must be present in Collection");
    }
    _values.splice(beforeIndex, 0, ...items2);
    me._indicesInvalid = 1;
    me.trigger("change", {
      action: "move",
      items: items2,
      from: itemIndex,
      to: beforeIndex
    });
    return beforeIndex;
  }
  /**
   * The core data set mutation method. Removes and adds at the same time. Analogous
   * to the `Array` `splice` method.
   *
   * Note that if items that are specified for removal are also in the `toAdd` array,
   * then those items are *not* removed then appended. They remain in the same position
   * relative to all remaining items.
   *
   * @param {Number} index Index at which to remove a block of items. Only valid if the
   * second, `toRemove` argument is a number.
   * @param {Object[]|Number} [toRemove] Either the number of items to remove starting
   * at the passed `index`, or an array of items to remove (If an array is passed, the `index` is ignored).
   * @param  {Object[]|Object} [toAdd] An item, or an array of items to add.
   */
  splice(index = 0, toRemove, ...toAdd) {
    const me = this, idProperty = me.idProperty, values = me._values, newIds = {}, replaced = [], oldCount = me.totalCount;
    let added, removed = [], mutated;
    if ((toAdd == null ? void 0 : toAdd.length) === 1 && Array.isArray(toAdd[0])) {
      toAdd = toAdd[0];
    }
    if (me.trigger("beforeSplice", { index, toRemove, toAdd }) === false) {
      return;
    }
    if (toAdd) {
      if (oldCount && toAdd.length) {
        const idIndex = me.indices[idProperty];
        added = [];
        for (let i = 0; i < toAdd.length; i++) {
          const newItem = toAdd[i], id2 = newItem[idProperty], existingItem = idIndex.get(id2), existingIndex = existingItem ? values.indexOf(existingItem) : -1;
          newIds[id2] = true;
          if (existingIndex !== -1) {
            if (values[existingIndex] !== newItem) {
              replaced.push([values[existingIndex], newItem]);
              values[existingIndex] = newItem;
            }
          } else {
            added.push(newItem);
          }
        }
      } else {
        added = toAdd;
      }
    }
    if (toRemove) {
      if (typeof toRemove === "number") {
        toRemove = Math.min(toRemove, values.length - index);
        if (!(toAdd == null ? void 0 : toAdd.length)) {
          removed = values.slice(index, index + toRemove);
          values.splice(index, toRemove);
          mutated = true;
        } else {
          for (let removeIndex = index; toRemove; --toRemove) {
            const id2 = values[removeIndex][idProperty];
            if (newIds[id2]) {
              index++;
              removeIndex++;
            } else {
              removed.push(values[removeIndex]);
              values.splice(removeIndex, 1);
              mutated = true;
            }
          }
        }
      } else {
        let contiguous = added.length === 0, lastIdx;
        toRemove = ArrayHelper.asArray(toRemove);
        const removeIndices = toRemove.reduce((result, item) => {
          const isNumeric = typeof item === "number", idx = isNumeric ? item : me.indexOf(item, true);
          if (contiguous && (lastIdx != null && idx !== lastIdx + 1 || isNumeric)) {
            contiguous = false;
          }
          if (idx >= 0 && idx < oldCount) {
            result.push(idx);
          }
          lastIdx = idx;
          return result;
        }, []).sort(reverseNumericSortFn);
        if (contiguous) {
          if (removeIndices.length) {
            removed.push.apply(removed, toRemove);
            values.splice(removeIndices[removeIndices.length - 1], removeIndices.length);
            mutated = true;
          }
        } else {
          for (let i = 0; i < removeIndices.length; i++) {
            const removeIndex = removeIndices[i];
            if (removeIndex !== -1) {
              const id2 = values[removeIndex][idProperty];
              if (!newIds[id2]) {
                removed.unshift(values[removeIndex]);
                values.splice(removeIndex, 1);
                mutated = true;
              }
            }
          }
        }
      }
      if (removed.length && !me._indicesInvalid) {
        removed.forEach(me.removeFromIndices, me);
      }
    }
    if (added.length) {
      ArrayHelper.chunkedInsert(values, Math.min(index, values.length), added);
      mutated = true;
      if (!me._indicesInvalid) {
        added.forEach(me.addToIndices, me);
      }
      if (!me._addedValues) {
        me._addedValues = /* @__PURE__ */ new Set();
      }
      for (const value2 of added) {
        me._addedValues.add(value2);
      }
    }
    if (removed.length && me._addedValues) {
      for (const value2 of removed) {
        me._addedValues.delete(value2);
      }
    }
    if (replaced.length && !me._indicesInvalid) {
      replaced.forEach((rep) => {
        me.removeFromIndices(rep[0]);
        me.addToIndices(rep[1]);
      });
    }
    if (mutated || replaced.length) {
      if (me.isSorted) {
        me.onSortersChanged();
      } else if (me.isFiltered) {
        if (me.autoFilter) {
          me.onFiltersChanged({ action: "splice", oldCount: 1 });
        } else {
          me._filteredValues.splice(Math.min(index, me._filteredValues.length), 0, ...added);
        }
      }
      me.generation++;
      me.trigger("change", {
        action: "splice",
        removed,
        added,
        replaced,
        oldCount
      });
    } else {
      me.trigger("noChange", {
        index,
        toRemove,
        toAdd
      });
    }
  }
  /**
   * Change the id of an existing member by mutating its {@link #config-idProperty}.
   * @param {String|Number|Object} item The item or id of the item to change.
   * @param {String|Number} newId The id to set in the existing member.
   */
  changeId(item, newId) {
    const me = this, { idProperty } = me, oldId = keyTypes[typeof item] ? item : item[idProperty], member = me.get(oldId);
    if (member) {
      const existingMember = me.get(newId);
      if (existingMember && member !== existingMember) {
        throw new Error(`Attempt to set item ${oldId} to already existing member's id ${newId}`);
      }
      me.removeIndexEntry(item, idProperty, oldId);
      me.addIndexEntry(item, idProperty, newId);
      member[idProperty] = newId;
    }
  }
  /**
   * Returns the item with the passed `id`. By default, filtered are honoured, and
   * if the item with the requested `id` is filtered out, nothing will be returned.
   *
   * To return the item even if it has been filtered out, pass the second parameter as `true`.
   * @param {*} id The `id` to find.
   * @param {Boolean} [ignoreFilters=false] Pass `true` to return an item even if it is filtered out.
   * @returns {Object} The found item, or `undefined`.
   */
  get(id2, ignoreFilters = false) {
    return this.getBy(this.idProperty, id2, ignoreFilters);
  }
  getAt(index, ignoreFilters = false) {
    if (this.isFiltered && !ignoreFilters) {
      return this._filteredValues[index];
    } else {
      return this._values[index];
    }
  }
  /**
   * Returns the item with passed property name equal to the passed value. By default,
   * filtered are honoured, and if the item with the requested `id` is filtered out,
   * nothing will be returned.
   *
   * To return the item even if it has been filtered out, pass the third parameter as `true`.
   * @param {String} propertyName The property to test.
   * @param {*} value The value to find.
   * @param {Boolean} [ignoreFilters=false] Pass `true` to return an item even if it is filtered out.
   * @returns {Object} The found item, or `undefined`.
   */
  getBy(propertyName, value2, ignoreFilters = false) {
    return this.findItem(propertyName, value2, this.isFiltered && ignoreFilters);
  }
  /**
   * The number of items in this collection. Note that this honours filtering.
   * See {@link #property-totalCount};
   * @property {Number}
   * @readonly
   */
  get count() {
    return this.values.length;
  }
  /**
   * The number of items in this collection regardless of filtering.
   * @property {Number}
   * @readonly
   */
  get totalCount() {
    return this._values.length;
  }
  /**
   * The property name used to extract item `id`s from added objects.
   * @member {String} idProperty
   */
  updateIdProperty(idProperty) {
    this.addIndex({ property: idProperty, unique: true });
  }
  //endregion
  //region Sorting
  /**
   * The Collection of {@link Core.util.CollectionSorter Sorters} for this Collection.
   * @member {Core.util.Collection} sorters
   */
  changeSorters(sorters) {
    return new _Collection({
      values: ArrayHelper.asArray(sorters),
      internalListeners: {
        change: "onSortersChanged",
        thisObj: this
      }
    });
  }
  /**
   * Adds a Sorter to the Collection of Sorters which are operating on this Collection.
   *
   * A Sorter may be specified as an instantiated {@link Core.util.CollectionSorter}, or a config object for a
   * CollectionSorter of the form
   *
   *     {
   *         property  : 'age',
   *         direction : 'desc'
   *     }
   *
   * Note that by default, a Sorter *replaces* a Sorter with the same `property` to make
   * it easy to change existing Sorters. A Sorter's `id` is its `property` by default. You
   * can avoid this and add multiple Sorters for one property by configuring Sorters with `id`s.
   *
   * A Sorter may also be specified as a function which compares two objects eg:
   *
   *     (lhs, rhs) => lhs.customerDetails.age - rhs.customerDetails.age
   *
   * @param {CollectionSorterConfig} sorter A Sorter configuration object to add to the Collection
   * of Sorters operating on this Collection.
   * @returns {Core.util.CollectionSorter} The resulting Sorter to make it easy to remove Sorters.
   */
  addSorter(sorter) {
    const result = sorter instanceof CollectionSorter ? sorter : new CollectionSorter(sorter);
    this.sorters.add(result);
    return result;
  }
  /**
   * A flag which is `true` if this Collection has active {@link #property-sorters}.
   * @property {Boolean}
   * @readonly
   */
  get isSorted() {
    var _a5;
    return Boolean((_a5 = this._sorters) == null ? void 0 : _a5.count);
  }
  onSortersChanged() {
    const me = this;
    me._sortFunction = null;
    me._addedValues = null;
    me._values.sort(me.sortFunction);
    me.trigger("change", sortEvent);
  }
  /**
   * A sorter function which encapsulates the {@link Core.util.CollectionSorter Sorters} for this Collection.
   * @property {Function}
   * @param {*} first First value to compare
   * @param {*} second Second value to compare
   * @returns {Number} Returns `1` if first value is a greater unit than second value, `-1` if the opposite is true or `0` if they're equal
   * @readonly
   */
  get sortFunction() {
    if (!this._sortFunction) {
      if (this.isSorted) {
        this._sortFunction = CollectionSorter.generateSortFunction(this.sorters.values);
      } else {
        this._sortFunction = return0;
      }
    }
    return this._sortFunction;
  }
  //endregion
  //region Filtering
  /**
   * The Collection of {@link Core.util.CollectionFilter Filters} for this Collection.
   * @property {Core.util.Collection}
   * @readonly
   */
  get filters() {
    if (!this._filters) {
      this._filters = new _Collection({
        internalListeners: {
          change: "onFiltersChanged",
          thisObj: this
        }
      });
    }
    return this._filters;
  }
  /**
   * Adds a Filter to the Collection of Filters which are operating on this Collection.
   *
   * A Filter may be an specified as an instantiated {@link Core.util.CollectionFilter
   * CollectionFilter}, or a config object for a CollectionFilter of the form
   *
   *     {
   *         property : 'age',
   *         operator : '>=',
   *         value    : 21
   *     }
   *
   * Note that by default, a Filter *replaces* a Filter with the same `property` to make
   * it easy to change existing Filters. A Filter's `id` is its `property` by default. You
   * can avoid this and add multiple Filters for one property by configuring Filters with `id`s.
   *
   * A Filter may also be specified as a function which filters candidate objects eg:
   *
   *     candidate => candidate.customerDetails.age >= 21
   *
   * @param {CollectionFilterConfig|Core.util.CollectionFilter} filter A Filter or Filter configuration object to add
   * to the Collection of Filters operating on this Collection.
   * @returns {Core.util.CollectionFilter} The resulting Filter to make it easy to remove Filters.
   */
  addFilter(filter2) {
    const result = filter2 instanceof CollectionFilter ? filter2 : new CollectionFilter(filter2);
    this.filters.add(result);
    return result;
  }
  removeFilter(filter2) {
    const { filters } = this;
    if (!filter2.isCollectionFilter) {
      filter2 = filters.get(filter2);
    }
    filters.remove(filter2);
  }
  clearFilters() {
    this.filters.clear();
  }
  /**
   * A flag which is `true` if this Collection has active {@link #property-filters}.
   * @property {Boolean}
   * @readonly
   */
  get isFiltered() {
    return Boolean(this._filters && this._filters.count);
  }
  onFiltersChanged({ action, removed: gone, oldCount }) {
    const me = this, oldDataset = oldCount || action === "clear" && gone.length ? me._filteredValues : me._values;
    me._filterFunction = null;
    me._filteredValues = me._values.filter(me.filterFunction);
    me._indicesInvalid = true;
    const {
      toAdd: added,
      toRemove: removed
    } = ArrayHelper.delta(me._filteredValues, oldDataset, true);
    me.trigger("change", { ...filterEvent, added, removed });
  }
  /**
   * A filter function which encapsulates the {@link Core.util.CollectionFilter Filters}
   * for this Collection.
   * @property {Function}
   * @param {*} value Value to compare
   * @returns {Boolean} Returns `true` for value valid for including
   * @readonly
   */
  get filterFunction() {
    if (!this._filterFunction) {
      if (this.isFiltered) {
        this._filterFunction = CollectionFilter.generateFiltersFunction(this.filters.values);
      } else {
        this._filterFunction = FunctionHelper.returnTrue;
      }
    }
    return this._filterFunction;
  }
  //endregion
  //region Indexing
  changeExtraKeys(extraKeys) {
    const keys = ArrayHelper.asArray(extraKeys);
    return keys.map((config) => {
      if (typeof config === "string") {
        return { property: config, unique: true };
      }
      return config;
    });
  }
  updateExtraKeys(extraKeys) {
    for (let i = 0; i < extraKeys.length; i++) {
      this.addIndex(extraKeys[i]);
    }
  }
  /**
   * Adds a lookup index for the passed property name or index config. The index is built lazily when an index is
   * searched
   * @internal
   * @param {Object} indexConfig An index config
   * @param {String} indexConfig.property The property name to add an index for
   * @param {Boolean} [indexConfig.unique] Specify `false` to allow multiple entries of the same index, turning
   *   entries into sets
   * @param {Object} [indexConfig.dependentOn] The properties that make the key
   */
  addIndex(indexConfig) {
    const me = this;
    if (indexConfig) {
      (me._indices || (me._indices = {}))[indexConfig.property] = /* @__PURE__ */ new Map();
      Object.assign(me._indices[indexConfig.property], indexConfig);
      me.invalidateIndices();
      if (indexConfig.dependentOn) {
        me.hasCompositeIndex = true;
      }
    }
  }
  /**
   * Return the index of the item with the specified key having the specified value.
   *
   * By default, filtering is taken into account and this returns the index in the filtered dataset if present. To
   * bypass this, pass the third parameter as `true`.
   *
   * Only useful for indices configured with `unique: true`.
   *
   * @param {String} propertyName The name of the property to test.
   * @param {*} value The value to test for.
   * @param {Boolean} [ignoreFilters=false] Pass `true` to return the index in
   * the original data set if the item is filtered out.
   * @returns {Number} The index of the item or `-1` if not found for unique indices
   */
  findIndex(propertyName, value2, ignoreFilters = false) {
    const item = this.findItem(propertyName, value2, ignoreFilters);
    if (!item) {
      return -1;
    }
    const values = this.isFiltered && !ignoreFilters ? this._filteredValues : this._values;
    return values.indexOf(item);
  }
  /**
   * Return the item with the specified key having the specified value.
   *
   * By default, filtering is taken into account. To bypass this, pass the third parameter as `true`.
   *
   * For indices configured with `unique: false`, a Set of items will be returned.
   *
   * @param {String} propertyName The name of the property to test.
   * @param {*} value The value to test for.
   * @param {Boolean} [ignoreFilters=false] Pass `true` to return the index in
   * the original data set if the item is filtered out.
   * @returns {Object|Set} The found item or Set of items or null
   */
  findItem(propertyName, value2, ignoreFilters = false) {
    var _a5;
    const me = this, { isFiltered } = me, index = isFiltered && !ignoreFilters ? me.indices[filteredIndicesProperty][propertyName] : me.indices[propertyName];
    if (index) {
      const item = (_a5 = index.get(value2)) != null ? _a5 : typeof value2 === "string" && value2.length && !isNaN(value2) && index.get(Number(value2)) || null;
      if (item != null) {
        return item;
      }
    } else {
      const values = isFiltered && !ignoreFilters ? me._filteredValues : me._values, count = values.length;
      for (let i = 0; i < count; i++) {
        const item = values[i];
        if (item[propertyName] == value2) {
          return item;
        }
      }
    }
    return null;
  }
  removeIndex(propertyName) {
    delete this._indices[propertyName];
    this.hasCompositeIndex = Object.values(this.indices).some((index) => index.dependentOn);
  }
  /**
   * Returns the index of the item with the same `id` as the passed item.
   *
   * By default, filtering is honoured, so if the item in question has been added, but is currently filtered out of
   * visibility, `-1` will be returned.
   *
   * To find the index in the master, unfiltered dataset, pass the second parameter as `true`;
   * @param {Object|String|Number} item The item to find, or an `id` to find.
   * @param {Boolean} [ignoreFilters=false] Pass `true` to find the index in the master, unfiltered data set.
   * @returns {Number} The index of the item, or `-1` if not found.
   */
  indexOf(item, ignoreFilters = false) {
    return this.findIndex(this.idProperty, keyTypes[typeof item] ? item : item[this.idProperty], ignoreFilters);
  }
  /**
   * Returns `true` if this Collection includes an item with the same `id` as the passed item.
   *
   * By default, filtering is honoured, so if the item in question has been added,
   * but is currently filtered out of visibility, `false` will be returned.
   *
   * To query inclusion in the master, unfiltered dataset, pass the second parameter as `true`;
   * @param {Object|String|Number} item The item to find, or an `id` to find.
   * @param {Boolean} [ignoreFilters=false] Pass `true` to find the index in the master, unfiltered data set.
   * @returns {Boolean} True if the passed item is found.
   */
  includes(item, ignoreFilters = false) {
    if (Array.isArray(item)) {
      return item.some((item2) => this.includes(item2));
    }
    return Boolean(this.findItem(this.idProperty, keyTypes[typeof item] ? item : item[this.idProperty], ignoreFilters));
  }
  get indices() {
    if (this._indicesInvalid) {
      this.rebuildIndices();
    }
    return this._indices;
  }
  invalidateIndices() {
    this._indicesInvalid = true;
  }
  /**
   * Called when the Collection is mutated and the indices have been flagged as invalid.
   *
   * Rebuilds the indices object to allow lookup by keys.
   * @internal
   */
  rebuildIndices() {
    const me = this, isFiltered = me.isFiltered, indices = me._indices || (me._indices = {}), keyProps = Object.keys(indices), indexCount = keyProps.length, values = me._values;
    let detectDuplicates = false, filteredIndices;
    if (isFiltered) {
      filteredIndices = indices[filteredIndicesProperty] = {};
    }
    for (let i = 0; i < indexCount; i++) {
      const index = indices[keyProps[i]];
      index.clear();
      if (index.onDuplicate) {
        index.count = 0;
        detectDuplicates = true;
      }
      if (isFiltered) {
        let filteredIndex = filteredIndices[keyProps[i]];
        if (filteredIndex) {
          filteredIndex.clear();
        } else {
          filteredIndex = filteredIndices[keyProps[i]] = /* @__PURE__ */ new Map();
          filteredIndex.unique = index.unique;
        }
      }
    }
    doRebuildIndices(values, indices, keyProps, indexCount);
    if (detectDuplicates) {
      for (let i = 0; i < indexCount; i++) {
        const index = indices[keyProps[i]];
        if (index.onDuplicate && index.count > index.size) {
          const checked = /* @__PURE__ */ new Set();
          for (let j = 0; j < values.length; j++) {
            const item = values[j], key = item[keyProps[i]];
            if (checked.has(key)) {
              index.onDuplicate(item, key, index);
            }
            checked.add(key);
          }
        }
      }
    }
    if (isFiltered) {
      doRebuildIndices(me._filteredValues, filteredIndices, keyProps, indexCount, true);
    }
    me._indicesInvalid = false;
  }
  // Returns an array with [indices] or [indices, filteredIndices] if filtering is used
  getIndices(propertyName) {
    const indices = [this.indices[propertyName]];
    if (this.isFiltered) {
      indices.push(this.indices[filteredIndicesProperty][propertyName]);
    }
    return indices;
  }
  /**
   * Add an item to all indices
   * @param {*} item Item already available in the Collection
   * @private
   */
  addToIndices(item) {
    Object.keys(this.indices).forEach((propertyName) => {
      this.addIndexEntry(item, propertyName, item[propertyName]);
    });
  }
  /**
   * Remove an item from all indices
   * @param {*} item Item already available in the Collection
   * @private
   */
  removeFromIndices(item) {
    Object.keys(this.indices).forEach((propertyName) => {
      this.removeIndexEntry(item, propertyName, item[propertyName]);
    });
  }
  /**
   * Remove an entry from an index, and if filtering is used also from the filtered index.
   * @param {*} item Item already available in the Collection
   * @param {String} propertyName Property of the item, will be matched with configured indices
   * @param {*} oldValue Value to remove
   * @private
   */
  removeIndexEntry(item, propertyName, oldValue) {
    this.getIndices(propertyName).forEach((index) => removeItemFromIndex(item, index, oldValue));
  }
  /**
   * Add a new entry to an index, and if filtering is used also to the filtered index.
   * @param {*} item Item already available in the Collection
   * @param {String} propertyName Property of the item, will be matched with configured indices
   * @param {*} value Value to store
   * @private
   */
  addIndexEntry(item, propertyName, value2) {
    this.getIndices(propertyName).forEach((index) => addItemToIndex(item, index, value2));
  }
  /**
   * Call externally to update indices on item mutation (from Store)
   * @param {*} item Item already available in the Collection
   * @param {Object} wasSet Uses the `wasSet` format from Store, `{ field : { oldValue, newValue } }`
   * @internal
   */
  onItemMutation(item, wasSet) {
    const me = this;
    if (!me._indicesInvalid && Object.keys(me.indices).length > 1) {
      Object.keys(wasSet).forEach((propertyName) => {
        var _a5;
        const indexConfig = me.indices[propertyName];
        if (indexConfig) {
          const { value: value2, oldValue } = wasSet[propertyName];
          me.removeIndexEntry(item, propertyName, oldValue);
          me.addIndexEntry(item, propertyName, value2);
        }
        if (me.hasCompositeIndex) {
          const dependentIndex = Object.values(me.indices).find((index) => {
            var _a6;
            return (_a6 = index.dependentOn) == null ? void 0 : _a6[propertyName];
          });
          if (dependentIndex) {
            const keysAndOldValues = {};
            for (const o in dependentIndex.dependentOn) {
              keysAndOldValues[o] = ((_a5 = wasSet[o]) == null ? void 0 : _a5.oldValue) || item[o];
            }
            const oldIndex = item.buildIndexKey(keysAndOldValues);
            me.removeIndexEntry(item, dependentIndex.property, oldIndex);
            me.addIndexEntry(item, dependentIndex.property, item[dependentIndex.property]);
          }
        }
      });
    }
  }
  //endregion
};
Collection._$name = "Collection";

// ../Core/lib/Core/util/Bag.js
var nonPrimitives = /* @__PURE__ */ new WeakMap();
var safeIndexKey = (value2) => {
  if (value2 && typeof value2 === "object") {
    let substitute = nonPrimitives.get(value2);
    if (substitute === void 0) {
      substitute = Symbol("bscik");
      nonPrimitives.set(value2, substitute);
    }
    value2 = substitute;
  }
  return value2;
};
var Bag = class {
  constructor(config) {
    const me = this;
    me.generation = 0;
    me.items = /* @__PURE__ */ new Set();
    me.idMap = {};
    me.idProperty = "id";
    if (config) {
      if (config.idProperty) {
        me.idProperty = config.idProperty;
      }
      if (config.values) {
        me.values = config.values;
      }
    }
  }
  /**
   * Returns the item with the passed `id`.
   *
   * @param {*} id The `id` to find.
   * @returns {Object} The found item, or `undefined`.
   */
  get(key) {
    return this.idMap[safeIndexKey(key)];
  }
  /**
   * The number of items in this Bag.
   * @property {Number}
   * @readonly
   */
  get count() {
    return this.items.size;
  }
  /**
   * Adds the passed item(s) to this Bag. Existing items with the same ID
   * will be replaced.
   * @param {Object|Object[]} toAdd Item(s) to add.
   */
  add(...toAdd) {
    if (toAdd.length === 1 && Array.isArray(toAdd[0])) {
      toAdd = toAdd[0];
    }
    const me = this, {
      items: items2,
      idMap,
      idProperty
    } = me, len = toAdd.length;
    for (let i = 0; i < len; i++) {
      const item = toAdd[i], key = keyTypes[typeof item] ? item : safeIndexKey(item[idProperty]), existingItem = idMap[key];
      if (existingItem == null) {
        idMap[key] = item;
        items2.add(item);
        me.generation++;
      } else if (existingItem !== item) {
        idMap[key] = item;
        items2.delete(existingItem);
        items2.add(item);
      }
    }
  }
  /**
   * Returns `nth` item in this Bag.
   * @param {Number} nth The index of the matching item. Negative numbers index for the last item. For example, -1
   * returns the last item, -2 the 2nd to last item etc.
   * @returns {Object} The matched item, or `undefined`.
   */
  at(nth) {
    let item, ret;
    if (nth < 0) {
      nth += this.count;
    }
    for (item of this.items) {
      if (!nth--) {
        ret = item;
        break;
      }
    }
    return ret;
  }
  /**
   * Removes the passed item(s) from this Bag.
   * @param {Object|Object[]} toRemove Item(s) to remove.
   */
  remove(toRemove) {
    toRemove = ArrayHelper.asArray(toRemove);
    const { items: items2, idMap, idProperty } = this, len = toRemove.length;
    for (let i = 0; i < len; i++) {
      const item = toRemove[i], key = keyTypes[typeof item] ? item : safeIndexKey(item[idProperty]), existingItem = idMap[key];
      if (existingItem != null) {
        items2.delete(existingItem);
        delete idMap[key];
        this.generation++;
      }
    }
  }
  clear() {
    this.items.clear();
    this.idMap = {};
    this.generation++;
  }
  /**
   * Returns the number of items in this Bag which elicits a truthy return value from the passed function.
   * @param {Function} fn A function, which, when passed an item, returns `true` to select it as the item to return.
   * @returns {Number} The number of matched items.
   */
  countOf(fn2) {
    let ret = 0, item;
    for (item of this.items) {
      if (fn2(item)) {
        ++ret;
      }
    }
    return ret;
  }
  /**
   * Change the id of an existing member by mutating its idProperty.
   * @param {String|Number|Object} item The item or id of the item to change.
   * @param {String|Number} newId The id to set in the existing member.
   */
  changeId(item, newId) {
    const me = this, { idMap, idProperty } = me, oldId = keyTypes[typeof item] ? item : safeIndexKey(item[idProperty]), member = me.get(oldId);
    if (member) {
      const existingMember = me.get(newId);
      if (existingMember && member !== existingMember) {
        throw new Error(`Attempt to set item ${oldId} to already existing member's id ${newId}`);
      }
      member[idProperty] = newId;
      delete idMap[oldId];
      idMap[newId] = member;
    }
  }
  /**
   * Extracts the matching items from this Bag into an array based upon the passed value filter function.
   * @param {Function} fn A function, which, when passed an item, returns a `true` to place into the resulting array.
   * @param {Object} [thisObj] The `this` reference when the function is called.
   * @returns {Object[]} An array of values extracted from this Bag.
   */
  filter(fn2, thisObj) {
    const { items: items2 } = this, result = [];
    let i = 0;
    items2.forEach((item) => {
      if (fn2.call(thisObj, item, i++, items2)) {
        result.push(item);
      }
    });
    return result;
  }
  /**
   * Returns `true` if this Collection includes an item with the same `id` as the passed item.
   *
   * @param {Object|String|Number} item The item to find, or an `id` to find.
   * @returns {Boolean} True if the passed item is found.
   */
  includes(item) {
    const key = keyTypes[typeof item] ? item : safeIndexKey(item[this.idProperty]);
    return Boolean(this.idMap[key]);
  }
  /**
   * Extracts the content of this Bag into an array based upon the passed
   * value extraction function.
   * @param {Function} fn A function, which, when passed an item, returns a value to place into the resulting array.
   * @param {Object} [thisObj] The `this` reference when the function is called.
   * @returns {Object[]} An array of values extracted from this Bag.
   */
  map(fn2, thisObj) {
    const { items: items2 } = this, result = new Array(items2.size);
    let i = 0;
    items2.forEach((item) => {
      result[i] = fn2.call(thisObj, item, i++, items2);
    });
    return result;
  }
  /**
   * Executes the passed function for each item in this Bag, passing in the item.
   * @param {Function} fn The function to execute.
   * @param {Object} [thisObj] The `this` reference when the function is called.
   */
  forEach(fn2, thisObj) {
    return this.items.forEach(fn2, thisObj);
  }
  /**
   * Returns `nth` item in this Bag which elicits a truthy return value from the provided matcher function `fn`.
   * @param {Function} fn A function which, when passed an item, returns `true` to select the item as a match.
   * @param {Number} [nth=0] The index of the matching item. By default, 0 returns the first item that matches
   * according to `fn`. Negative numbers index for the last item. For example, -1 returns the last matching item,
   * -2 the 2nd to last matching item etc..
   * @returns {Object} The matched item, or `undefined`.
   */
  find(fn2, nth = 0) {
    let item, ret;
    if (nth < 0) {
      nth += this.countOf(fn2);
    }
    for (item of this.items) {
      if (fn2(item) && !nth--) {
        ret = item;
        break;
      }
    }
    return ret;
  }
  /**
   * Iterator that allows you to do for (let item of bag)
   */
  [Symbol.iterator]() {
    return this.items[Symbol.iterator]();
  }
  indexOf(item, matchFn) {
    let index = -1, it;
    for (it of this.items) {
      if (!matchFn || matchFn(it)) {
        ++index;
        if (it === item) {
          return index;
        }
      }
    }
    return -1;
  }
  /**
   * The set of values of this Bag.
   *
   * Setting this property replaces the data set.
   * @property {Object[]}
   */
  get values() {
    return [...this.items];
  }
  set values(values) {
    values = ArrayHelper.asArray(values);
    this.clear();
    this.add.apply(this, values);
    this.generation++;
  }
  get first() {
    for (const item of this.items) {
      return item;
    }
    return null;
  }
  get last() {
    let ret = null;
    for (const item of this.items) {
      ret = item;
    }
    return ret;
  }
  /**
   * Sort the values of this Bag using the passed comparison function.
   *
   * Setting this property replaces the data set.
   * @param {Function} fn Comparison function which returns -ve, 0, or +ve
   */
  sort(fn2) {
    this.values = this.values.sort(fn2);
  }
  some(fn2, thisObj) {
    return this.values.some(fn2, thisObj);
  }
};
Bag._$name = "Bag";

// ../Core/lib/Core/data/StoreBag.js
var StoreBag = class extends Bag {
  add(...toAdd) {
    if (toAdd.length === 1 && Array.isArray(toAdd[0])) {
      toAdd = toAdd[0];
    }
    return super.add(...toAdd.filter((record) => record.isPersistable));
  }
};
StoreBag._$name = "StoreBag";

// ../Core/lib/Core/data/mixin/StoreCRUD.js
var sortSiblingsByParentIndexFn = (a, b) => {
  const aModified = a.meta.modified, bModified = b.meta.modified;
  if ("parentId" in aModified && "parentId" in bModified && // sort record inside the same parent
  aModified.parentId === bModified.parentId) {
    return aModified.parentIndex - bModified.parentIndex;
  }
  return 0;
};
var StoreCRUD_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    //region Config
    static get defaultConfig() {
      return {
        /**
         * Commit changes automatically
         * @config {Boolean}
         * @default
         * @category Common
         */
        autoCommit: false
      };
    }
    static get properties() {
      return {
        isRemoving: false,
        suspendCount: 0
      };
    }
    //endregion
    //region Events
    /**
     * Fired after removing all records
     * @event removeAll
     * @param {Core.data.Store} source This Store
     */
    /**
     * Fired before committing changes. Return false from handler to abort commit
     * @event beforeCommit
     * @param {Core.data.Store} source This Store
     * @param {Object} changes Modification data
     */
    /**
     * Fired after committing changes
     * @event commit
     * @param {Core.data.Store} source This Store
     * @param {Object} changes Modification data
     */
    /**
     * Fired before records are removed from this store by the {@link #function-remove} or {@link #function-removeAll}.
     * Also fired when removing a child record in a tree store using {@link Core.data.mixin.TreeNode#function-removeChild}.
     * The remove may be vetoed by returning `false` from a handler.
     * @event beforeRemove
     * @param {Core.data.Store} source This Store
     * @param {Core.data.Model[]} records The records which are to be removed.
     * @param {Core.data.Model} parent The record from which children are being removed when using a tree store. Only
     * provided when removing a single node.
     * @param {Boolean} isMove This flag is `true` if the child node is being removed by
     * {@link Core.data.mixin.TreeNode#function-appendChild appendChild} to be moved
     * _within the same tree_.
     * @param {Boolean} removingAll This flag is `true` if the operation is removing the store's entire data set.
     * @preventable
     */
    /**
     * Fired before records are added to this store by the {@link #function-add} or {@link #function-insert}. In a tree
     * store, also fired by {@link Core.data.mixin.TreeNode#function-appendChild} and
     * {@link Core.data.mixin.TreeNode#function-insertChild}. The add or insert may be vetoed by returning `false`
     * from a handler.
     * @event beforeAdd
     * @param {Core.data.Store} source This Store
     * @param {Core.data.Model[]} records The records which are to be added
     * @param {Core.data.Model} parent The parent node when using a tree store
     * @preventable
     */
    /**
     * Fired after adding/inserting record(s). If the record was added to a parent, the `isChild` flag is set on the
     * event. If it was inserted, event contains `index`
     * @event add
     * @param {Core.data.Store} source This Store
     * @param {Core.data.Model[]} records Added records. In case of tree store, if branch is added, only branch root
     * is returned
     * @param {Core.data.Model[]} [allRecords] Flat list of all added records. In case of tree store, if branch is
     * added, all new records are returned, not only branch root
     * @param {Core.data.Model} [parent] If due to an {@link Core/data/mixin/TreeNode#function-appendChild}
     * call, this is the parent node added to.
     * @param {Number} [index] Insertion point in the store's {@link Core.data.Store#config-storage Collection}.
     * @param {Number} [oldIndex] Not used for tree stores. The index of the first record moved.
     * @param {Boolean} [isChild] Flag which is set to `true` if the records are added to a parent record
     * @param {Boolean} [isExpand] Flag which is set to `true` if records are added to the store by expanding parent
     * @param {Object} [isMove] An object keyed by the ids of the records which were moved from another
     * position in the store, or from another parent node in the store. The ids of moved records will be
     * property names with a value `true`.
     */
    /**
     * Fired when one or more records are removed
     * @event remove
     * @param {Core.data.Store} source This Store
     * @param {Core.data.Model[]} records Array of removed records. In case of tree store, if branch is removed, only branch root
     * is returned
     * @param {Core.data.Model[]} [allRecords] Flat array of all removed records. In case of tree store, if branch is
     * removed, all removed records are returned, not only branch root
     * @param {Core.data.Model} [parent] If due to a {@link Core.data.mixin.TreeNode#function-removeChild removeChild}
     * call, this is the parent node removed from. Only applicable when removing a single tree node.
     * @param {Number} [index] Visible index at which record was removed. In case the record was removed from a collapsed
     * branch, -1 is returned. For tree store, this is only provided when removing a single node.
     * @param {Boolean} [isChild] Flag which is set to `true` if the record was removed from a parent record
     * @param {Boolean} [isCollapse] Flag which is set to `true` if records were removed from the store by collapsing parent
     * @param {Boolean} [isMove] Passed as `true` if the remove was part of a move operation within this Store.
     */
    /**
     * Fired when Data in the store was changed. See {@link Core/data/Store#event-change} event for the details.
     * @event change
     * @param {Core.data.Store} source This Store
     * @param {'remove'|'removeAll'|'add'|'updatemultiple'|'clearchanges'|'filter'|'update'|'dataset'|'replace'} action
     * Name of action which triggered the change. May be one of the options listed above
     * @param {Core.data.Model} record Changed record, for actions that affects exactly one record (`'update'`)
     * @param {Core.data.Model[]} records Changed records, passed for all actions except `'removeAll'`
     * @param {Object} changes Passed for the `'update'` action, info on which record fields changed
     */
    /**
     * Data in the store has completely changed, such as by a filter, or sort or load operation.
     * @event refresh
     * @param {Core.data.Store} source This Store.
     * @param {Boolean} batch Flag set to `true` when the refresh is triggered by ending a batch
     * @param {'dataset'|'sort'|'clearchanges'|'filter'|'create'|'update'|'delete'|'group'} action Name of
     * action which triggered the change. May be one of the options listed above.
     */
    //endregion
    //region Add, insert & remove
    /**
     * Removes a record from this store. Fires a single {@link #event-remove} event passing the removed records.
     * @param {String|String[]|Number|Number[]|Core.data.Model|Core.data.Model[]} records Record/array of records (or record ids) to remove
     * @param {Boolean} [silent] Specify `true` to suppress events/autoCommit
     * @returns {Core.data.Model[]} Removed records
     * @fires beforeRemove
     * @fires remove
     * @fires change
     * @category CRUD
     */
    remove(records, silent = false) {
      const me = this, { storage } = me;
      records = ArrayHelper.asArray(records).reduce((result, r) => {
        r = me.getById(r);
        if (r) {
          result.push(r);
        }
        return result;
      }, []);
      if (records.length === 0) {
        return records;
      }
      if (!me.tree && !silent && me.trigger("beforeRemove", { records }) === false) {
        return [];
      }
      me.isRemoving = true;
      if (me.isGrouped) {
        const oldCount = storage.count, recordsInCollapsedGroups = [], changedGroupParents = /* @__PURE__ */ new Set();
        for (const rec of records) {
          const { groupParent } = rec.instanceMeta(me);
          if (groupParent.meta.collapsed) {
            recordsInCollapsedGroups.push(rec);
          }
          ArrayHelper.remove(groupParent.groupChildren, rec);
          ArrayHelper.remove(groupParent.unfilteredGroupChildren, rec);
          groupParent.meta.childCount--;
          changedGroupParents.add(groupParent);
        }
        for (const groupParent of changedGroupParents) {
          if (groupParent.groupChildren.length > 0) {
            me.onModelChange(groupParent, {}, {});
          }
        }
        if (recordsInCollapsedGroups.length) {
          storage.trigger("change", {
            action: "splice",
            removed: recordsInCollapsedGroups,
            added: [],
            replaced: [],
            oldCount
          });
        }
      } else if (me.tree) {
        const allRemovedRecords = [], affectedParents = /* @__PURE__ */ new Set(), removingMultiple = records.length > 1, firstRemoved = records[0], index = removingMultiple ? void 0 : storage.indexOf(firstRemoved), removeChildArgs = records.reduce((result, child) => {
          const { parent } = child;
          if (parent) {
            if (!result[parent.id]) {
              result[parent.id] = [parent, []];
            }
            result[parent.id][1].push(child);
          }
          return result;
        }, {}), removals = Array.from(Object.values(removeChildArgs));
        if (!silent && me.trigger("beforeRemove", { records, isMove: false, parent: removingMultiple ? void 0 : firstRemoved.parent }) === false) {
          me.isRemoving = false;
          return [];
        }
        me.suspendAutoCommit();
        removals.sort((a, b) => b[0].childLevel - a[0].childLevel);
        for (const [parent, children] of removals) {
          const removedChildren = parent.removeChild(children, false, true, { skipIndexUpdate: true });
          ArrayHelper.chunkedPush(allRemovedRecords, removedChildren);
          affectedParents.add(parent);
        }
        for (const parent of affectedParents) {
          if (!allRemovedRecords.includes(parent)) {
            parent.updateChildrenIndices(parent.children, "parentIndex");
            parent.unfilteredChildren && parent.updateChildrenIndices(parent.unfilteredChildren, "unfilteredIndex");
            parent.orderedChildren && parent.updateChildrenIndices(parent.orderedChildren, "orderedParentIndex");
          }
        }
        if (!silent) {
          me.trigger("remove", {
            // parent is only relevant when removing single node
            parent: removingMultiple ? void 0 : firstRemoved.parent,
            index,
            isChild: true,
            allRecords: allRemovedRecords,
            isMove: false,
            records
          });
          me.trigger("change", {
            action: "remove",
            records
          });
        }
        me.resumeAutoCommit();
        me.isRemoving = false;
        return records;
      }
      if (records.length === 0) {
        me.isRemoving = false;
        return records;
      }
      for (const record of records) {
        record.beforeRemove(records);
      }
      if (silent) {
        me.suspendEvents();
      }
      storage.remove(records);
      if (silent) {
        me.resumeEvents();
      }
      if (me.autoCommit) {
        me.doAutoCommit();
      }
      me.isRemoving = false;
      return records;
    }
    /**
     * Clears store data. Used by removeAll, separate function for using with chained stores.
     * @private
     * @category CRUD
     */
    clear(isLoading = false) {
      var _a6, _b2;
      const me = this, { storage } = me;
      if (me.storage.totalCount || ((_b2 = (_a6 = me.rootNode) == null ? void 0 : _a6.unfilteredChildren) == null ? void 0 : _b2.length)) {
        if (!isLoading && me.trigger("beforeRemove", { records: storage.allValues, removingAll: true }) === false) {
          return null;
        }
        if (me.rootNode) {
          if (me.isChained && !me.isChainedTree) {
            const allRecords = me.getAllDataRecords();
            for (let i = allRecords.length - 1, rec; i >= 0; i--) {
              rec = allRecords[i];
              if (rec && !rec.isDestroyed) {
                rec.unjoinStore(me);
              }
            }
          } else {
            me.rootNode.clearChildren(isLoading);
          }
        } else if (isLoading) {
          const allRecords = me.registeredRecords;
          for (let i = allRecords.length - 1, rec; i >= 0; i--) {
            rec = allRecords[i];
            if (rec && !rec.isDestroyed) {
              rec.unjoinStore(me);
            }
          }
        }
        if (isLoading) {
          me.removed.clear();
          storage.suspendEvents();
        }
        storage.clear();
        if (isLoading) {
          storage.resumeEvents();
        }
        me.added.clear();
        me.modified.clear();
      }
    }
    /**
     * Removes all records from the store.
     *
     * {@note}If called on a lazy-loaded store, it removes all the loaded records. And if a backend is configured, then those deletions
     * will also be synced with the backend.{/@note}
     * @param {Boolean} [silent] Specify `true` to suppress events
     * @returns {Boolean} `true` unless the action was prevented, in which case it returns `false`
     * @fires beforeRemove
     * @fires removeAll
     * @fires change
     * @category CRUD
     * @non-lazy-load
     */
    removeAll(silent = false) {
      const me = this, storage = me.storage;
      let result;
      me.isRemoving = true;
      if (silent) {
        storage.suspendEvents();
        const allRecords = me.registeredRecords;
        for (let i = allRecords.length - 1, rec; i >= 0; i--) {
          rec = allRecords[i];
          if (rec && !rec.isDestroyed && !rec.isRoot) {
            rec.unjoinStore(me);
          }
        }
      }
      if (me.tree) {
        result = me.rootNode.clear() !== false;
      } else {
        result = me.clear() !== null;
      }
      if (silent) {
        storage.resumeEvents();
      }
      me.isRemoving = false;
      return result;
    }
    /**
     * Add records to store.
     * @param {Core.data.Model|Core.data.Model[]|Object|Object[]} records Array of records/data or a single record/data to add to store
     * @param {Boolean} [silent] Specify `true` to suppress events
     * @returns {Core.data.Model[]} Added records
     * @fires add
     * @fires change
     * @category CRUD
     */
    add(records, silent = false, options = {}) {
      const me = this, { storage } = me;
      records = ArrayHelper.asArray(records);
      if (!(records == null ? void 0 : records.length)) {
        return;
      }
      me.tree = me.tree || Boolean(me.autoTree && records[0].children);
      if (me.tree) {
        const parentIdMap = /* @__PURE__ */ new Map(), parentIndexField = me.modelClass.getFieldDataSource("parentIndex"), orderedIndexField = me.modelClass.getFieldDataSource("orderedParentIndex"), { parentIdField } = me.modelClass, added2 = [];
        let indexField;
        records.forEach((node) => {
          indexField = indexField || (!(parentIndexField in node) && orderedIndexField in node ? orderedIndexField : parentIndexField);
          const parentId = node[parentIdField];
          if (!parentIdMap.has(parentId)) {
            parentIdMap.set(parentId, { append: [], insert: [] });
          }
          const entry = parentIdMap.get(parentId);
          if (!node.isModel && indexField in node) {
            entry.insert.push(node);
          } else {
            entry.append.push(node);
          }
        });
        parentIdMap.forEach(({ append, insert }, parentId) => {
          const parentNode = parentId == null ? me.rootNode : me.getById(parentId);
          if (!parentNode) {
            throw new Error(`Parent node with id ${parentId} not found, cannot add children.`);
          }
          if (append.length) {
            const addedChildren = parentNode.appendChild(append, silent, options);
            ArrayHelper.chunkedPush(added2, addedChildren);
          }
          if (insert.length) {
            for (const toInsert of insert) {
              if ("orderedParentIndex" in toInsert) {
                if ("orderedParentIndex" in options && ObjectHelper.isObject(options.orderedParentIndex)) {
                  options.orderedParentIndex.index = toInsert.orderedParentIndex;
                } else {
                  options.orderedParentIndex = toInsert.orderedParentIndex;
                }
              }
              added2.push(parentNode.insertChild(toInsert, toInsert[indexField], silent, options));
            }
          }
        });
        if (me.isFiltered && me.reapplyFilterOnAdd) {
          me.filter({
            silent: true
          });
        }
        if (me.reapplySortersOnAdd && !me.isSyncingDataOnLoad) {
          me.sort();
        }
        return added2;
      }
      if (!silent && me.trigger("beforeAdd", { records }) === false) {
        return null;
      }
      me.tree = me.tree || Boolean(me.autoTree && records[0].children);
      if (me.tree) {
        return me.rootNode.appendChild(records);
      }
      const added = me.processRecords(records);
      if (silent) {
        me.suspendEvents();
      }
      me.addingClean = options.clean;
      storage.add(added);
      me.addingClean = false;
      if (silent) {
        me.resumeEvents();
      }
      if (me.autoCommit) {
        me.doAutoCommit();
      }
      return added;
    }
    // Helper fn used by processRecords() below and TreeNode.ingestChildren()
    cloneImmutable(records) {
      if (this.useRawData.enabled && !records[0].isModel && !Object.isExtensible(records[0])) {
        records = ObjectHelper.clone(records);
      }
      return records;
    }
    processRecords(records, onRecordCreated) {
      return this.cloneImmutable(records).map((data) => {
        const record = this.processRecord(data.isModel ? data : this.createRecord(data));
        onRecordCreated == null ? void 0 : onRecordCreated.call(this, record);
        return record;
      });
    }
    /**
     * Insert records into the store.
     * @param {Number} index Index to insert at
     * @param {Core.data.Model|Core.data.Model[]|Object|Object[]} records Array of records/data or a single record/data to insert to store
     * @param {Boolean} [silent] Specify `true` to suppress events
     * @returns {Core.data.Model[]} Inserted records
     * @fires add
     * @fires change
     * @category CRUD
     */
    insert(index, records, silent = false) {
      var _a6;
      const me = this, storage = me.storage, insertBefore = storage.getAt(index), _records = storage.values, removeIndices = [];
      records = ArrayHelper.asArray(records);
      if (me.tree) {
        const root = me.rootNode;
        return root.insertChild(records, (_a6 = root.children) == null ? void 0 : _a6[index], silent);
      }
      if (!silent && me.trigger("beforeAdd", { records }) === false) {
        return null;
      }
      let isNoop, start, i;
      if (_records[start = index] === records[0] || _records[start = index - 1] === records[0]) {
        for (isNoop = true, i = 0; isNoop && i < records.length; i++) {
          if (records[i] !== _records[start + i]) {
            isNoop = false;
          }
        }
      }
      if (isNoop) {
        return null;
      }
      const added = me.processRecords(records, (record) => {
        const removedAtIndex = storage.indexOf(record);
        if (record.children && record.children.length && me.autoTree) {
          me.tree = true;
        }
        if (removedAtIndex > -1) {
          if (removedAtIndex < index && insertBefore)
            index--;
          removeIndices.push(removedAtIndex);
        }
        record.meta.previousIndex = removedAtIndex;
      });
      me.suspendEvents();
      me.storage.remove(removeIndices);
      me.resumeEvents();
      if (silent) {
        me.suspendEvents();
      }
      storage.splice(index, 0, added);
      if (silent) {
        me.resumeEvents();
      }
      if (me.autoCommit) {
        me.doAutoCommit();
      }
      return added;
    }
    /**
     * Moves a record, or block of records to another location.
     * @param {Core.data.Model|Core.data.Model[]} records The record(s) to move.
     * @param {Core.data.Model} beforeRecord the record to insert the first record(s) before.
     * @fires change
     * @category CRUD
     */
    move(records, beforeRecord) {
      const me = this;
      if (me.isTree) {
        beforeRecord.parent.insertChild(records, beforeRecord);
      } else {
        me.storage.move(records, beforeRecord);
        if (me.isChained && me.syncOrder) {
          me.$master.storage.move(records, beforeRecord);
        }
      }
    }
    //endregion
    //region Commit
    /**
     * Accepts all changes, resets the modification tracking:
     * * Clears change tracking for all records
     * * Clears added
     * * Clears modified
     * * Clears removed
     * Leaves the store in an "unmodified" state.
     * @internal
     */
    acceptChanges() {
      const me = this;
      me.added.forEach((record) => record.internalClearChanges(true, false));
      me.modified.forEach((record) => record.internalClearChanges(true, false));
      me.added.clear();
      me.modified.clear();
      me.removed.clear();
    }
    /**
     * Commits changes, per default only returns changes and resets tracking.
     * @param {Boolean} [silent] Specify `true` to suppress events
     * @returns {Object} Changes, see {@link #property-changes}
     * @fires beforeCommit
     * @fires commit
     * @category CRUD
     */
    commit(silent = false) {
      const { changes } = this;
      if (!silent && this.trigger("beforeCommit", { changes }) === false) {
        return false;
      }
      this.acceptChanges();
      if (!silent) {
        this.trigger("commit", { changes });
      }
      return changes;
    }
    /**
     * Reverts all changes in the store (adds removed records back, and removes newly added records).
     * @fires change
     * @fires refresh
     * @category CRUD
     */
    revertChanges() {
      const me = this, { changes } = me;
      if (changes) {
        const event = { action: "clearchanges", changes }, modified = me.modified.values;
        me.add(me.removed.values, true);
        me.remove(me.added.values, true);
        modified.sort(sortSiblingsByParentIndexFn);
        modified.forEach((r) => r.revertChanges(true));
        me.added.clear();
        me.modified.clear();
        me.removed.clear();
        me.trigger("change", event);
        me.trigger("refresh", event);
      }
    }
    /**
     * Get uncommitted changes as an object of added/modified/removed arrays of records.
     *
     * ```javascript
     * // Format:
     * {
     *      added: [], // array of Core.data.Model
     *      modified: [], // array of Core.data.Model
     *      removed: [] // array of Core.data.Model
     * }
     * ```
     *
     * @member {Object} changes
     * @property {Core.data.Model[]} changes.added Records that have been added
     * @property {Core.data.Model[]} changes.modified Records that have changes to persistable fields
     * @property {Core.data.Model[]} changes.removed Records that have been removed
     * @readonly
     * @category Records
     */
    get changes() {
      const me = this, modified = me.modified.values.filter((r) => r.rawModifications);
      return me.added.count || modified.length || me.removed.count ? {
        // Slicing to have changes intact when triggering commit
        added: me.added.values.slice(),
        modified,
        removed: me.removed.values.slice()
      } : null;
    }
    /**
     * Boolean flag, indicating whether the store has any data changes (its {@link #property-changes} accessor
     * returns non-empty object). Cheaper than {@link #property-changes} accessor itself, because it
     * does not clone some internal data structures.
     *
     * @property {Boolean}
     * @readonly
     * @category Records
     */
    get hasChanges() {
      return Boolean(this.added.count || this.removed.count || this.modified.values.some((r) => r.rawModifications));
    }
    /**
     * Setting autoCommit to true automatically commits changes to records.
     * @property {Boolean}
     * @category Records
     */
    get autoCommit() {
      return this._autoCommit;
    }
    set autoCommit(auto) {
      this._autoCommit = auto;
      if (auto && this.hasChanges) {
        this.commit();
      }
    }
    /**
     * Suspends automatic commits upon store changes. Can be called multiple times (it uses an internal counter).
     * @category Records
     */
    suspendAutoCommit() {
      this.suspendCount++;
    }
    /**
     * Resumes automatic commits upon store changes. Will trigger commit if the internal counter is 0.
     * @category Records
     */
    resumeAutoCommit(doCommit = true) {
      this.suspendCount--;
      if (this.autoCommit && doCommit) {
        this.doAutoCommit();
      }
    }
    doAutoCommit() {
      if (this.suspendCount <= 0) {
        this.commit();
      }
    }
    //endregion
  }, __publicField(_a5, "$name", "StoreCRUD"), _a5;
};

// ../Core/lib/Core/data/mixin/StoreChanges.js
var StoreChanges_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    static get configurable() {
      return {
        /**
         * Specifies target to filter and sort after applying changeset:
         * * `'changes'` - apply sort and filter to changeset only (see more below)
         * * `'none'` - do not apply sort and filter
         *
         * ### `changes` behavior
         * If the store has filters in effect when the changeset is applied, the following rules will determine how the
         * filtered values are affected:
         * - Among added records, only those that match the filter will be included in the filtered set
         * - Among updated records, those that did not previously match the filter but now do will be added to the filtered set,
         *   and those that did match but no longer do will also remain in the filtered set. This means that new records may
         *   appear in the filtered set as a result of `applyChanges`, but records will not disappear until filters are
         *   re-applied.
         *
         * Does not apply if the store is {@link Core/data/Store#config-remoteFilter remotely filtered}.
         * @default
         * @prp {'changes'|'none'}
         * @category Advanced
         */
        applyChangesetFilterSortTarget: "changes"
      };
    }
    /**
     * Applies changes from another store to this store. Useful if cloning records in one store to display in a
     * grid in a popup etc. to reflect back changes.
     * @param {Core.data.Store} otherStore
     * @category CRUD
     */
    applyChangesFromStore(otherStore) {
      const me = this, { changes } = otherStore;
      if (!changes) {
        return;
      }
      if (changes.added) {
        me.add(changes.added);
      }
      if (changes.removed) {
        me.remove(changes.removed.map((r) => r.id));
      }
      if (changes.modified) {
        changes.modified.forEach((record) => {
          const localRecord = me.getById(record.id);
          localRecord == null ? void 0 : localRecord.set(record.modifications);
        });
      }
    }
    /**
     * Applies a set of changes (presumable from a backend) expressed as an object matching the format outputted by the
     * {@link Core/data/Store#property-changes} property: `{ added : [], modified/updated : [], removed : [] }`
     *
     * `added` is expected to be an array of raw data objects consumable by the stores model class for records to add to
     * the store (see example snippet below).
     *
     * `modified` (or `updated` for compatibility with Schedulers CrudManager) is expected to have the same format as
     * `added`, but should always include the `id` of the record to update.
     *
     * Records that have been created locally and gets assigned a proper id by the backend are expected to also pass a
     * `phantomId` field (name of the field is configurable using the `phantomIdField` arg, more info on phantom ids
     * below), to match it with the current id of a local record (`id` will contain the new id).
     *
     * Note that it is also possible to pass this `phantomId` -> `id` mapping in the `added` array. When encountering a
     * record in that array that already exists in the local store, it will be treated the same was as a record in the
     * `modified` array.
     *
     * `removed` is expected to be an array of objects with the `{ id : xx }` shape. Any matches on an id in the store
     * will be removed, those and any non matches will also be cleared from the change tracking of the store.
     *
     * If the store has filters in effect when the changeset is applied, the following rules will determine how the
     * filtered values are affected:
     * - Among added records, only those that match the filter will be included in the filtered set
     * - Among updated records, those that did not previously match the filter but now do will be added to the filtered set,
     *   and those that did match but no longer do will also remain in the filtered set. This means that new records may
     *   appear in the filtered set as a result of `applyChanges`, but records will not disappear until filters are
     *   re-applied.
     *
     * As an example, consider a store with the following initial state and some operations performed on it:
     *
     * ```javascript
     * // Load some data into the store
     * store.data = [
     *     { id : 1, name : 'Minerva' },
     *     { id : 2, name : 'Mars' },
     *     { id : 3, name : 'Jupiter' }
     * ];
     * // Add a new record. It gets assigned a generated id,
     * // for example 'generated56'
     * store.add({ name : 'Artemis' });
     * // Remove Jupiter
     * store.remove(3);
     * ```
     *
     * After syncing those operations to a custom backend (however you chose to solve it in your application) we might
     * get the following response (see "Transforming a response to the correct format" below if your backend responds
     * in another format):
     *
     * ```javascript
     * const serverResponse = {
     *     added : [
     *         // Added by the backend, will be added locally
     *         { id : 5, name : 'Demeter' }
     *     ],
     *
     *     updated : [
     *         // Will change the name of Minerva -> Athena
     *         { id : 1, name : 'Athena' },
     *         // Will set proper id 4 for Artemis
     *         { $PhantomId : 'generated56', id : 4 }
     *     ],
     *
     *     removed : [
     *         // Confirmed remove of Jupiter
     *         { id : 3 },
     *         // Removed by the backend, Mars will be removed locally
     *         { id : 2 }
     *     ]
     * };
     * ```
     *
     * If that response is then passed to this function:
     *
     * ```javascript
     * store.applyChangeSet(serverResponse);
     * ```
     *
     * The end result will be the following data in the store:
     *
     * ```javascript
     * [
     *     { id : 1, name : 'Athena' }, // Changed name
     *     { id : 4, name : 'Artemis' }, // Got a proper id
     *     { id : 5, name : 'Demeter' } // Added by the backend
     * ]
     * ```
     *
     * ### Phantom ids
     *
     * When a record is created locally, it is always assigned a generated id. That id is called a phantom id (note that
     * it is assigned to the normal id field). When passing the new record to the backend, the id is sent with it. When
     * the backend inserts the record into the database, it (normally) gets a proper id assigned. That id then needs to
     * be passed back in the response, to update the local record with the correct id. Making sure that future updates
     * match the correct row in the database.
     *
     * For example a newly created record should be passed similar to this to the backend (pseudo format, up to the
     * application/backend to decide):
     *
     * ```json
     * {
     *     "added" : {
     *         "id" : "generated79",
     *         "name" : "Hercules",
     *         ...
     *     }
     * }
     * ```
     *
     * For the backend response to be applicable for this function, it should then respond with:
     *
     * ```json
     * {
     *     "updated" : {
     *         {
     *             "$PhantomId" : "generated79",
     *             "id" : 465
     *         }
     *     }
     * }
     * ```
     *
     * (Or, as stated above, it can also be passed in the "added" array. Which ever suits your backend best).
     *
     * This function will then change the id of the local record using the phantom id `generated79` to `465`.
     *
     * ### Transforming a response to the correct format
     *
     * This function optionally accepts a `transformFn`, a function that will be called with the `changes`. It is
     * expected to return a changeset in the format described above (`{ added : [], updated : [], removed : [] }`),
     * which then will be used to apply the changes.
     *
     * Consider the following "non standard" (made up) changeset:
     *
     * ```javascript
     * const changes = {
     *     // Database ids for records previously added locally
     *     assignedIds : {
     *         'phantom1' : 10,
     *         'phantom2' : 15
     *     },
     *     // Ids records removed by the backend
     *     removed : [11, 27],
     *     // Modified records, keyed by id
     *     altered : {
     *         12 : { name : 'Changed' }
     *     },
     *     // New records, keyed by id
     *     inserted : {
     *         20  : { name : 'New' }
     *     }
     * }
     * ```
     *
     * Since it does not match the expected format it has to be transformed:
     *
     * ```javascript
     * store.applyChangeset(changes, ({ assignedIds, inserted, altered, removed }) => ({
     *    // Convert inserted to [{ id : 20, name : 'New' }]
     *    added : Object.entries(inserted).map(([id, data] => ({ id, ...data }),
     *    updated : [
     *        // Convert assignedIds to [{ $PhantomId : 'phantom1', id : 10 }, ...]
     *       ...Object.entries(assignedIds).map(([phantomId, id])) => ({ $PhantomId : phantomId, id }),
     *       // Convert altered to [{ id : 12, name : 'Changed' }]
     *       ...Object.entries(modified).map(([id, data] => ({ id, ...data })
     *    ],
     *    // Convert removed to [{ id : 11 }, ...]
     *    removed : removed.map(id => ({ id }))
     * }));
     * ```
     *
     * The transform function above would output:
     *
     * ```javascript
     * {
     *     added : [
     *         {  id : 20, name : 'New' }
     *     ],
     *     updated : [
     *         { $PhantomId : 'phantom1', id : 10 },
     *         { $PhantomId : 'phantom2', id : 15 },
     *         {  id : 12, name : 'Changed' }
     *     ],
     *     removed : [
     *        { id : 11 },
     *        { id : 12 }
     *     ]
     * }
     * ```
     *
     * And that format can then be applied.
     *
     * @param {Object} changes Changeset to apply to the store, see specification above
     * @param {Function} [transformFn] Optional function used to preprocess a changeset in a different format,
     * should return an object with the format expected by this function (see above)
     * @param {String} [phantomIdField] Field used by the backend when communicating a record being assigned a proper id
     * instead of a phantom id (see above)
     * @privateparam {Boolean} [remote] Set to `true` to indicate changes are from the remote source. Remote changes have
     * precedence over local.
     * @privateparam {Boolean} [logChanges] Used by CrudManager to be able to revert specific changes later
     * @category CRUD
     */
    applyChangeset(changes, transformFn = null, phantomIdField = "$PhantomId", remote = true, logChanges = false) {
      var _a6, _b2, _c2, _d, _e, _f;
      const me = this, transformedChanges = (_a6 = transformFn == null ? void 0 : transformFn(changes, me)) != null ? _a6 : changes, input = transformedChanges.$input || transformedChanges, {
        added,
        updated,
        modified,
        removed
      } = input, altered = (_b2 = updated != null ? updated : modified) != null ? _b2 : [], idDataSource = me.modelClass.getFieldDataSource("id"), parentIdDataSource = me.modelClass.getFieldDataSource("parentId"), log = logChanges ? /* @__PURE__ */ new Map() : null, isTree = me.tree, allAdded = [], allAltered = [], modifiedOrderedParentNodes = /* @__PURE__ */ new Set();
      let rootUpdated = false, modifiedParents = [];
      me.trigger("startApplyChangeset");
      me._groupVisibleRecordIds = [];
      me.isGrouped && me.forEach((record) => {
        me._groupVisibleRecordIds.push(record.id);
      });
      if ((added == null ? void 0 : added.length) > 0) {
        const toUpdate = [], toAdd = [];
        for (const data of added) {
          const record = me.getById(data[phantomIdField]) || me.getById(ObjectHelper.getPath(data, idDataSource));
          if (record) {
            toUpdate.push(data);
            isTree && modifiedOrderedParentNodes.add(record.parent);
          } else {
            const record2 = me.removed.get(data[phantomIdField]) || me.removed.get(ObjectHelper.getPath(data, idDataSource));
            if (!record2) {
              toAdd.push(data);
            }
          }
        }
        altered.unshift.apply(altered, toUpdate);
        const prevReapplySortersOnAdd = me.reapplySortersOnAdd;
        me.reapplySortersOnAdd = false;
        const addedRecords = (_c2 = me.add(toAdd, false, { orderedParentIndex: { skip: true }, clean: remote })) != null ? _c2 : [];
        ArrayHelper.chunkedPush(allAdded, addedRecords);
        me.reapplySortersOnAdd = prevReapplySortersOnAdd;
        if (isTree) {
          for (const record of addedRecords) {
            const { parent } = record;
            if (parent.isRoot) {
              rootUpdated = true;
              modifiedParents = [parent];
              break;
            }
            if (!parent.isRoot && modifiedParents.every((r) => !r.contains(parent))) {
              modifiedParents.push(parent);
            }
            if ("orderedParentIndex" in record.originalData) {
              modifiedOrderedParentNodes.add(parent);
            }
          }
        }
        for (const record of addedRecords) {
          log == null ? void 0 : log.set(record.id, record.data);
          record.internalClearChanges();
        }
        isTree && modifiedOrderedParentNodes.forEach((parent) => {
          parent.updateChildrenIndices(parent.orderedChildren, "orderedParentIndex", true);
        });
      }
      if ((altered == null ? void 0 : altered.length) > 0) {
        for (const data of altered) {
          const phantomId = data[phantomIdField], id2 = ObjectHelper.getPath(data, idDataSource), parentId = ObjectHelper.getPath(data, parentIdDataSource), removedRecord = (_d = me.removed.get(phantomId)) != null ? _d : me.removed.get(id2), record = (_e = removedRecord != null ? removedRecord : me.getById(phantomId)) != null ? _e : me.getById(id2);
          if (record) {
            const newParentWasLeaf = parentId != null ? (_f = me.getById(parentId)) == null ? void 0 : _f.isLeaf : false;
            this.filterTreeHierarchyChanges(modifiedOrderedParentNodes, record, data);
            const changes2 = record.applyChangeset(data, phantomIdField);
            if (removedRecord && idDataSource in changes2 && phantomId) {
              me.onRecordIdChange({ record, oldValue: phantomId, value: id2 });
            }
            if (newParentWasLeaf) {
              me.toggleCollapse(record.parent, false);
            }
            if (isTree && record.parent && !rootUpdated && modifiedParents.every((r) => !r.contains(record))) {
              if (record.parent.isRoot) {
                rootUpdated = true;
                modifiedParents = [record.parent];
              } else {
                modifiedParents.push(record.parent);
              }
            }
            log == null ? void 0 : log.set(id2, changes2);
            allAltered.push(record);
          }
        }
      }
      if ((removed == null ? void 0 : removed.length) > 0) {
        me.applyRemovals(removed);
      }
      if (me.applyChangesetFilterSortTarget === "changes" && !me.remoteFilter) {
        const parentsModifiedByFilter = me.filterChangeset(allAdded, allAltered);
        modifiedParents.push(...parentsModifiedByFilter);
      }
      me.afterChangesetApplied(modifiedParents);
      if (isTree && me.isSorted && me.reapplySortersOnAdd && ((added == null ? void 0 : added.length) || (altered == null ? void 0 : altered.length))) {
        const refreshed = /* @__PURE__ */ new Set();
        modifiedParents.forEach((record) => {
          if (refreshed.has(record)) {
            return;
          }
          refreshed.add(record);
          record.sortVisibleChildren(false, me.sorterFn);
          record.sortUnfilteredChildren(false, me.sorterFn);
        });
        me.storage.replaceValues({
          values: me.collectDescendants(me.rootNode).visible,
          silent: true
        });
        me.afterPerformSort(false);
      }
      me._groupVisibleRecordIds = null;
      me.trigger("endApplyChangeset");
      return log;
    }
    afterChangesetApplied(modifiedParents) {
      const visited = /* @__PURE__ */ new Set();
      modifiedParents.forEach((parent) => {
        if (visited.has(parent)) {
          return;
        }
        parent.traverse((record) => {
          if (visited.has(record)) {
            return;
          }
          visited.add(record);
          record.sortOrderedChildren(false, false);
          if (record.children) {
            record.updateChildrenIndices(record.children, "parentIndex", true);
          }
          if (record.unfilteredChildren) {
            record.updateChildrenIndices(record.unfilteredChildren, "unfilteredIndex", true);
          }
        });
      });
    }
    // Apply removals, removing records and updating the `removed` bag to match.
    //
    // Accepts an array of objects containing an `id` property. Records in the store matching an entry in the array
    // will be removed from the store and the `removed` bag. Unmatched entries will be removed from the `removed` bag.
    applyRemovals(removals) {
      const me = this, { removed: removedStash } = me, idDataSource = me.modelClass.idField, toRemove = [];
      for (const removedEntry of removals) {
        const id2 = ObjectHelper.getPath(removedEntry, idDataSource);
        if (removedStash.includes(id2)) {
          const record = removedStash.get(id2);
          record.internalClearChanges(false, true, null);
          record.$isPhantom = true;
          removedStash.remove(id2);
        } else {
          toRemove.push(id2);
        }
      }
      if (me.tree) {
        const modifiedParents = toRemove.reduce((parents, id2) => {
          var _a6;
          if (me.getById(id2)) {
            parents.add((_a6 = me.getById(id2).parentNode) != null ? _a6 : me.rootNode);
          }
          return parents;
        }, /* @__PURE__ */ new Set());
        modifiedParents.forEach((parent) => parent.sortOrderedChildren(false, false));
      }
      me.remove(toRemove);
      for (const record of toRemove) {
        removedStash.remove(record);
      }
    }
    /**
     * Filters records that have been added/updated as part of a changeset. The `added` and `updated` parameters
     * are arrays of values that have already been added/updated in the Collection's values. This method brings
     * the Collection's `_filteredValues` in sync without performing a full sort or filter, using the following rules:
     *
     * - Added records that do not match the filter are removed from _filteredValues
     *
     * - Updated records that now match the filter are inserted at the correct position in _filteredValues
     *   if they were not formerly included
     *
     * - Updated records that formerly matched the filter, but now do not, are NOT removed from _filteredValues
     *
     * If the collection is sorted, either on its own or via a sort applied at the store level, that sort order is
     * respected when adding items to _filteredValues. If not, items are inserted in the same order they occur in
     * _values.
     *
     * @param {Object[]} added An array of unique values that were added as part of the changeset.
     * @param {Object[]} updated An array of unique values that were updated as part of the changeset.
     * @returns {Object[]} Any records that were added or removed from view, or whose children were modified.
     * @private
     */
    filterChangeset(added, updated) {
      const me = this, {
        isFiltered,
        tree,
        isGrouped,
        filtersFunction
      } = me, storeSortFunction = me.isSorted ? me.createSorterFn(me.sorters) : void 0, {
        allValues,
        addedValues,
        isSorted
      } = me.storage, sorter = storeSortFunction != null || isSorted ? storeSortFunction != null ? storeSortFunction : me.storage.sortFunction : null, modifiedParents = /* @__PURE__ */ new Set();
      if (!isFiltered) {
        return [];
      }
      let trigger = false, groupers;
      if (isGrouped) {
        groupers = me.groupers;
        me.clearGroupers(true);
      }
      if (tree) {
        const nodesToInclude = new Set(updated.filter(filtersFunction));
        for (const matchingAdd of added.filter(filtersFunction)) {
          nodesToInclude.add(matchingAdd);
        }
        nodesToInclude.forEach((node) => node.bubble((ancestor) => nodesToInclude.add(ancestor)));
        nodesToInclude.delete(me.rootNode);
        const nodesToIncludeByParent = ArrayHelper.groupByIndexed(
          // filter out the nodes that are already included to `children`
          Array.from(nodesToInclude).filter((child) => {
            var _a6, _b2;
            return !((_b2 = (_a6 = child.parent) == null ? void 0 : _a6.children) == null ? void 0 : _b2.includes(child));
          }),
          (r) => r.parent
        );
        for (const [parent, siblingsToInclude] of nodesToIncludeByParent) {
          if (parent.unfilteredChildren) {
            parent.children.push(...siblingsToInclude);
            modifiedParents.add(parent);
            me.traverseFilter(parent, false, siblingsToInclude);
          }
        }
      } else if (sorter && !isGrouped) {
        const { filteredValues } = me.storage, sortedLength = addedValues ? filteredValues.findLastIndex((value2) => !addedValues.has(value2)) + 1 : filteredValues.length, sorted = filteredValues.slice(0, sortedLength), updatedMatches = new Set(updated.filter(filtersFunction));
        for (const value2 of filteredValues) {
          if (updatedMatches.has(value2)) {
            updatedMatches.delete(value2);
          }
        }
        for (const newMatch of updatedMatches) {
          sorted.push(newMatch);
        }
        sorted.sort(sorter);
        filteredValues.splice(0, sortedLength, ...sorted);
        trigger = true;
      } else {
        const updatedMatches = updated.filter((item) => filtersFunction(item) && !me.storage.includes(item));
        if (updatedMatches.length > 0) {
          me.includeInSubset(allValues, me.storage.filteredValues, updatedMatches);
          trigger = true;
        }
      }
      const nonMatchingAdds = new Set(added.filter((value2) => !filtersFunction(value2)));
      if (nonMatchingAdds.size > 0) {
        if (tree) {
          for (const addedChild of nonMatchingAdds) {
            ArrayHelper.remove(addedChild.parent.children, addedChild);
            modifiedParents.add(addedChild.parent);
          }
        } else {
          ArrayHelper.remove(me.storage.filteredValues, nonMatchingAdds);
        }
        trigger = true;
      }
      if (groupers) {
        me.group(groupers[0], null, false, true, true);
        trigger = false;
        me._idMap = null;
      }
      if (tree && modifiedParents.size > 0) {
        me.storage.replaceValues({
          values: me.collectDescendants(me.rootNode).visible,
          silent: true
        });
      } else if (trigger) {
        me._idMap = null;
        me.trigger("refresh");
      }
      return Array.from(modifiedParents);
    }
    /**
     * This method is required to filter out unnecessary orderedParentIndex/parentIndex changes. Those change when we
     * reorder nodes or append new records. If we reorder nodes, we need to apply those changes and then sort local
     * array according to contents. But if we added nodes, we should ignore such changes in siblings of the added nodes
     * and should recalculate indices right away.
     * @param {Set} modifiedParents Set of parent record {@link Core.data.Model} which have new children
     * @param {Core.data.Model} record Record to which we apply changes
     * @param {Object} data Changes which we are about to apply
     * @private
     */
    filterTreeHierarchyChanges(modifiedParents, record, data) {
      if (this.tree && modifiedParents.has(record.parent)) {
        delete data.orderedParentIndex;
        delete data.parentIndex;
      }
    }
    /**
     * Given an array `all`, an array `subset` that is a subset of `all` in the same order, and another array
     * `toInclude` that is a different subset of `all` disjoint with `subset`, add each item from `toInclude`
     * to `subset`, in an order matching the order in `all`. The order of `subset` must match the order of `all`.
     * The order of `toInclude` is unimportant.
     *
     * Modifies `subset` in-place.
     *
     * @param {Array} all An array of unique items (e.g. records)
     * @param {Array} subset An array containing a subset of the items in `all` (same order as `all`)
     * @param {Array} toInclude An array or items from `all` that should be included in `subset` (unordered)
     * @returns {Array} The subset modified in-place.
     * @private
     */
    includeInSubset(all, subset, toInclude) {
      const toIncludeSet = new Set(toInclude);
      let subsetIndex = 0, allIndex = 0, done = toIncludeSet.size === 0;
      while (allIndex < all.length && !done) {
        const subsetItem = subset[subsetIndex];
        let allItem = all[allIndex];
        while (subsetItem !== allItem) {
          if (toIncludeSet.has(allItem)) {
            subset.splice(subsetIndex, 0, allItem);
            subsetIndex++;
            toIncludeSet.delete(allItem);
            done = toIncludeSet.size === 0;
          }
          allItem = all[++allIndex];
        }
        if (toIncludeSet.has(subsetItem)) {
          toIncludeSet.delete(subsetItem);
        }
        if (subsetIndex < subset.length) {
          subsetIndex++;
        }
      }
      return subset;
    }
  }, __publicField(_a5, "$name", "StoreChanges"), _a5;
};

// ../Core/lib/Core/data/mixin/StoreFilter.js
var StoreFilter_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    static get defaultConfig() {
      return {
        /**
         * Specify one or more {@link Core/util/CollectionFilter} config objects to apply initially.
         *
         * For example:
         *
         * ```javascript
         *  // Configure the store to filter in clients over the age of 30
         *  new Store({
         *      ...,
         *      filters : [{
         *          property : 'age',
         *          value    : 30,
         *          operator : '>'
         *      }],
         *      ...
         *  })
         * ```
         *
         * or:
         *
         * ```javascript
         *  // Configure the store to filter based on a complex operation
         *  new Store({
         *      ...,
         *      filters : [{
         *          filterBy(record) {
         *              // Return true or false for filtering in or out
         *              return shouldClientBeVisible(record);
         *          }
         *      }],
         *      ...
         *  })
         * ```
         *
         * @config {CollectionFilterConfig|CollectionFilterConfig[]}
         * @category Filtering
         */
        filters: null,
        /**
         * Specify true to reapply filters when a record is added to the store.
         * @config {Boolean}
         * @default
         * @category Filtering
         */
        reapplyFilterOnAdd: false,
        /**
         * Specify `true` to reapply filters when a record is updated in the store. You can also provide an array
         * of field names, to only re-filter when certain data fields are updated.
         * @config {Boolean|Object} reapplyFilterOnUpdate
         * @property {String[]} reapplyFilterOnUpdate.fields
         * @default
         * @category Filtering
         */
        reapplyFilterOnUpdate: false
      };
    }
    //endregion
    //region Events
    /**
     * Fired before filtering
     * @event beforeFilter
     * @param {Core.data.Store} source This Store
     * @param {Core.util.Collection} filters Filters to be applied to this Store
     */
    /**
     * Fired after applying filters to the store
     * @event filter
     * @param {Core.data.Store} source This Store
     * @param {Core.util.Collection} filters Filters used by this Store
     * @param {Core.data.Model[]} removed The records which were filtered out by the action.
     * @param {Core.data.Model[]} added The records which were filtered back in by the action.
     * @param {Core.data.Model[]} records Filtered records
     */
    //endregion
    //region Properties
    updateFilterParamName(param) {
      this.remoteFilter = param != null;
    }
    set reapplyFilterOnAdd(enable) {
      this.storage.autoFilter = enable;
    }
    get reapplyFilterOnAdd() {
      return this.storage.autoFilter;
    }
    /**
     * Currently applied filters. A collection of {@link Core.util.CollectionFilter} instances.
     * @type {Core.util.Collection}
     * @readonly
     * @category Sort, group & filter
     */
    set filters(filters) {
      const me = this, collection = me.filters;
      collection.clear();
      me._filtersFunction = null;
      if (filters) {
        if (filters.constructor.name === "Object") {
          for (const f of Object.entries(filters)) {
            if (f[0] === "filterBy" && typeof f[1] === "function") {
              collection.add(new CollectionFilter({
                filterBy: f[1]
              }));
            } else {
              collection.add(new CollectionFilter(f[1].constructor.name === "Object" ? Object.assign({
                property: f[0]
              }, f[1]) : {
                property: f[0],
                value: f[1]
              }));
            }
          }
        } else if (Array.isArray(filters)) {
          collection.add(...filters.map((filterConfig) => {
            if (filterConfig instanceof CollectionFilter) {
              return filterConfig;
            }
            return new CollectionFilter(filterConfig);
          }));
        } else if (filters.isCollection) {
          collection.add(...filters.values);
        } else {
          collection.add(new CollectionFilter({
            filterBy: filters
          }));
        }
        collection.forEach((item) => item.owner = me);
      }
    }
    get filters() {
      return this._filters || (this._filters = new Collection({ extraKeys: ["property"] }));
    }
    set filtersFunction(filtersFunction) {
      this._filtersFunction = filtersFunction;
    }
    get filtersFunction() {
      const me = this, { filters, isGrouped } = me;
      if (!me._filtersFunction) {
        if (filters.count) {
          const generatedFilterFunction = CollectionFilter.generateFiltersFunction(filters);
          me._filtersFunction = (candidate) => {
            if (isGrouped && candidate.isSpecialRow) {
              return candidate.groupChildren.some(generatedFilterFunction);
            }
            if (candidate.isCreating) {
              return true;
            }
            return generatedFilterFunction(candidate);
          };
        } else {
          me._filtersFunction = FunctionHelper.returnTrue;
        }
      }
      return me._filtersFunction;
    }
    /**
     * Check if store is filtered
     * @property {Boolean}
     * @readonly
     * @category Sort, group & filter
     */
    get isFiltered() {
      if (!this._filters && (!this.isConfiguring || !this.initialConfig.filters)) {
        return false;
      }
      return this.filters.values.some((filter2) => !filter2.disabled);
    }
    isFilteredOut(record) {
      return this.isFiltered && !this.filtersFunction(record);
    }
    //endregion
    traverseFilter(record, deep = true, forceInclusionSet = void 0) {
      const me = this, { filtersFunction } = me, hitsCurrent = !record.isRoot && filtersFunction(record), children = record.unfilteredChildren || record.children;
      if (!children || !children.length) {
        return hitsCurrent;
      }
      if (!record.unfilteredChildren) {
        record.unfilteredChildren = record.children.slice();
      }
      record.children = record.unfilteredChildren.filter(
        (r) => deep ? me.traverseFilter(r, deep) : filtersFunction(r) || (forceInclusionSet == null ? void 0 : forceInclusionSet.has(r))
      );
      if (me.isSorted) {
        record.children.sort(me.sorterFn);
      }
      record.updateChildrenIndices(record.unfilteredChildren, "unfilteredIndex", true);
      record.updateChildrenIndices(record.children, "parentIndex", true);
      return hitsCurrent || Boolean(record.children.length);
    }
    traverseClearFilter(record) {
      const me = this;
      if (record.unfilteredChildren) {
        record.children = record.orderedChildren.slice();
        record.unfilteredChildren = null;
      }
      if (record.children) {
        if (me.isSorted) {
          record.children.sort(me.sorterFn);
        }
        record.children.forEach((r) => me.traverseClearFilter(r));
        record.updateChildrenIndices(record.children, "parentIndex", true);
      }
    }
    get latestFilterField() {
      return this.filters.last ? this.filters.last.property : null;
    }
    /**
     * Adds a single filter to the {@link #config-filters} collection. By default, filters are reevaluated
     * and a Store change event fired.
     *
     * If the `silent` parameter is passed as `true`, multiple filters can be added without causing data changes.
     *
     * When the filters are as required, call {@link #function-filter} with no parameters
     * to apply the filters to the store.
     *
     * @param {CollectionFilterConfig|Function} newFilter A {@link Core.util.CollectionFilter filter} config,
     * or a function to use for filtering.
     * @param {Boolean} [silent] Pass `true` to *not* refilter the store immediately. Such as when
     * adding multiple filters.
     * @returns {Promise|Core.util.CollectionFilter} If {@link Core/data/AjaxStore#config-filterParamName} is set on store, this method
     * returns Collection filter inside a `Promise` which is resolved after data is loaded from remote server, otherwise it returns `null`
     * @async
     * @category Sort, group & filter
     */
    addFilter(filter2, silent = false) {
      const me = this;
      filter2 = filter2 instanceof CollectionFilter ? filter2 : new CollectionFilter(filter2);
      filter2.owner = me;
      me.filters.add(filter2);
      if (!silent) {
        if (me.remoteFilter) {
          return me.filter().then(() => filter2);
        } else {
          me.filter();
        }
      }
      return filter2;
    }
    /**
     * Filters the store by **adding** the specified filter(s) to the existing filters collection applied to this Store.
     * If a filter has an {@link Core.util.CollectionFilter#config-id id} specified,
     * or a {@link Core.util.CollectionFilter#config-property property} specified,
     * it will search for corresponding filter(s) in the existing filters first and replace it with a new filter.
     * **It will not remove other filters applied to the store!**
     *
     * To **add** a new filter:
     *
     * ```javascript
     * // Filter using simple object
     * store.filter({
     *     property : 'age',
     *     operator : '>',
     *     value    : 90
     * });
     *
     * // Filter using function
     * store.filter(r => r.age < 90);
     *
     * // Filter using a named filter as a function
     * store.filter({
     *     id : 'my-filter',
     *     filterBy : record => record.score > 10
     * });
     * ```
     *
     * To **remove** a specific filter, but keep other filters applied
     *
     * ```javascript
     * // Remove by filter `id` or `property`. Filter `id` defaults to the `property-operator` value.
     * store.removeFilter('age');
     * store.removeFilter('my-filter');
     * ```
     *
     * To **replace** all existing filters with a new filter
     *
     * ```javascript
     * // Remove all filters and filter using simple object
     * store.filter({
     *     filters : {
     *         property : 'age',
     *         operator : '<',
     *         value    : 90
     *     },
     *     replace : true
     * });
     *
     * // Remove all filters and filter using function
     * store.filter({
     *     filters : r => r.age > 90,
     *     replace : true
     * });
     *
     * // Remove all filters and filter using a named filter as a function
     * store.filter({
     *     filters : {
     *         id : 'my-filter',
     *         filterBy : record => record.score > 10
     *     },
     *     replace : true
     * });
     * ```
     *
     * Basically filters replacing is an equivalent of having two sequenced calls:
     * {@link #function-clearFilters clearFilters} and {@link #function-filter filter}.
     *
     * Call without arguments to reapply filters.
     *
     * ```javascript
     * // Re-filter the store
     * store.filter();
     * ```
     *
     * @param {Object|CollectionFilterConfig|CollectionFilterConfig[]|Function} newFilters
     *        A {@link Core.util.CollectionFilter filter} config,
     *        or an array of {@link Core.util.CollectionFilter filter} configs,
     *        or a function to use for filtering,
     *        or a special object like: ```{ replace : true, filters : newFilters }```
     * @param {Boolean} [newFilters.replace]
     *        A flag, indicating whether or not the previous filters should be removed.
     * @param {Boolean} [newFilters.silent]
     *        Set as true to not fire events. UI will not be informed about the changes.
     * @param {CollectionFilterConfig|CollectionFilterConfig[]|Function} [newFilters.filters]
     *        If `newFilters` is an object and `replace` property is defined in the `newFilters`,
     *        it means that special object is used and real filter configuration must be nested down to this `filters` property.
     *        It can be:
     *        A {@link Core.util.CollectionFilter filter} config,
     *        or an array of {@link Core.util.CollectionFilter filter} configs,
     *        or a function to use for filtering.
     * @fires filter
     * @fires Core.data.Store#event-change
     * @returns {Promise|null} If {@link Core/data/AjaxStore#config-filterParamName} is set on store, this method returns Promise
     * which is resolved after data is loaded from remote server, otherwise it returns null value
     * @async
     * @category Sort, group & filter
     */
    filter(newFilters) {
      const me = this;
      let silent = false, internal;
      if (newFilters) {
        let fieldType = typeof newFilters;
        if (fieldType === "object") {
          if ("silent" in newFilters || "replace" in newFilters || newFilters.filters) {
            silent = newFilters.silent;
            if (newFilters.replace) {
              me.clearFilters(newFilters.silent || newFilters.filters.length !== 0);
            }
            internal = newFilters.internal;
            newFilters = newFilters.filters;
            fieldType = typeof newFilters;
          }
        }
        if (newFilters) {
          const wasFiltered = me.isFiltered;
          me.isConfiguring = true;
          if (Array.isArray(newFilters)) {
            newFilters.forEach((f) => me.addFilter(f, true), me);
          } else if (fieldType === "function") {
            const filter2 = new CollectionFilter(newFilters);
            filter2.internal = internal;
            me.addFilter(filter2, true);
          } else if (fieldType === "string") {
            me.addFilter({
              property: newFilters,
              value: arguments[1]
            }, true);
          } else {
            me.addFilter(newFilters, true);
          }
          me.isConfiguring = false;
          if (!me.isFiltered && !wasFiltered) {
            return null;
          }
        }
      }
      me.filtersFunction = null;
      const result = me.remoteFilter ? me.performRemoteFilter(silent) : me.performFilter(silent);
      me._idMap = null;
      return result;
    }
    suspendFilterOnUpdate() {
      this.filterBatching++;
    }
    resumeFilterOnUpdate(silent) {
      this.filterBatching--;
      if (this.filterBatching < 0) {
        throw new Error("No matching `startFilterBatching` call");
      }
      if (this.filterBatching === 0 && this.needsFiltering) {
        this.needsFiltering = false;
        this.performFilter(silent);
      }
    }
    /**
     * Perform filtering according to the {@link #property-filters} Collection.
     * This is the internal implementation which is overridden in {@link Core.data.AjaxStore} and
     * must not be overridden.
     * @private
     */
    performFilter(silent) {
      const me = this;
      if (me.filterBatching > 0) {
        me.needsFiltering = true;
        return;
      }
      const { storage, filters, rootNode } = me, oldCount = me.count;
      if (!silent) {
        me.trigger("beforeFilter", { filters });
      }
      let added, removed;
      if (me.tree) {
        const oldDataset = storage.values;
        if (me.isFiltered) {
          me.traverseFilter(rootNode);
        } else {
          me.traverseClearFilter(rootNode);
        }
        const newDataset = me.collectDescendants(rootNode).visible;
        storage.replaceValues({
          values: newDataset,
          silent: true
        });
        const delta = ArrayHelper.delta(newDataset, oldDataset, true);
        added = delta.toAdd;
        removed = delta.toRemove;
      } else {
        storage.ion({
          change({ removed: r, added: a }) {
            removed = r;
            added = a;
          },
          once: true
        });
        if (me.isFiltered) {
          me.isGrouped && me.includeCollapsed();
          storage.addFilter({
            id: "primary-filter",
            filterBy: me.filtersFunction
          });
          me.isGrouped && me.excludeCollapsed();
        } else {
          storage.filters.clear();
        }
      }
      me.afterPerformFilter(silent || me.isRemoteDataLoading ? null : {
        action: "filter",
        filters,
        oldCount,
        added,
        removed,
        records: me.storage.values
      });
    }
    async performRemoteFilter(silent) {
      var _a6;
      const me = this, {
        filters,
        count: oldCount
      } = me;
      if (await ((_a6 = me.beforePerformFilter) == null ? void 0 : _a6.call(me, { filters, oldCount })) === false) {
        return;
      }
      me.currentPage = 1;
      if (await me.performDataRequest(silent) === false) {
        return;
      }
      const event = silent ? null : {
        action: "filter",
        filters,
        oldCount,
        records: me.storage.values
      };
      me.afterPerformFilter(event);
      me.trigger("refresh", event);
    }
    buildRemoteParams(params = {}, encode = false) {
      var _a6, _b2;
      (_a6 = super.buildRemoteParams) == null ? void 0 : _a6.call(this, params, encode);
      const me = this, { filters } = me;
      if (me.isFiltered) {
        params[(_b2 = me.filterParamName) != null ? _b2 : "filters"] = encode ? me.encodeFilterParams(filters) : me.getFilterParams(filters);
      }
      return params;
    }
    afterPerformFilter(event) {
      this.resetRelationCache();
      if (event) {
        this.triggerFilterEvent(event);
      }
    }
    getFilterParams(filters) {
      return filters.values.filter((f) => !f.disabled).map(({ property, operator, value: value2, caseSensitive }) => ({
        field: property,
        operator,
        value: value2,
        caseSensitive
      }));
    }
    // Used from filter() and StoreCRUD when reapplying filters
    triggerFilterEvent(event) {
      this.trigger("filter", event);
      if (!this.remoteFilter) {
        this.trigger("refresh", event);
        this.trigger("change", event);
      }
    }
    /**
     * *Adds* a function used to filter the store. Alias for calling `filter(fn)`. Return `true` from the function to
     * include record in filtered set
     *
     * ```javascript
     * store.filterBy(record => record.age > 25 && record.name.startsWith('A'));
     * ```
     *
     * @param {Function} fn Function used to test records
     * @returns {Promise|null} If {@link Core/data/AjaxStore#config-filterParamName} is set on store, this method returns `Promise`
     * which is resolved after data is loaded from remote server, otherwise it returns `null`
     * @async
     * @category Sort, group & filter
     */
    filterBy(fn2) {
      return this.filter(fn2);
    }
    /**
     * Removes the passed filter, or the filter by the passed ID from the {@link #config-filters} collection.
     * By default, filters are reevaluated and a Store change event fired.
     *
     * If the `silent` parameter is passed as `true`, multiple filters can be removed without causing data changes.
     *
     * When the filters are as required, call {@link #function-filter} with no parameters
     * to apply the filters to the store.
     *
     * ```javascript
     * // Only view top priority events
     * myEventStore.filter({
     *     id       : 'priorityFilter',
     *     property : 'priority',
     *     value    : 1,
     *     operator : '='
     * });
     *
     * // That individual filter can be removed like this
     * myEventStore.removeFilter('priorityFilter');
     *
     * // Add named filter as a function
     * store.filter({
     *     id : 'my filter',
     *     filterBy : record => record.score > 10
     * });
     *
     * // Remove named filter function
     * store.removeFilter('my filter');
     * ```
     *
     * @param {String|Core.util.CollectionFilter} idOrInstance Filter to remove, or ID of the filter to remove. By default,
     * filters are reevaluated and a change event fired.
     * @param {Boolean} [silent] Pass `true` to *not* refilter the store immediately. Such as when
     * removing multiple filters.
     * @returns {Promise|Core.util.CollectionFilter} If {@link Core/data/AjaxStore#config-filterParamName} is set on store, this method
     * returns Collection filter inside a `Promise` which is resolved after data is loaded from remote server, otherwise it returns `null`
     * @async
     * @category Sort, group & filter
     */
    removeFilter(idOrInstance, silent = false) {
      var _a6, _b2;
      const me = this, filter2 = idOrInstance instanceof CollectionFilter ? idOrInstance : (_a6 = me.filters.get(idOrInstance)) != null ? _a6 : me.filters.getBy("property", idOrInstance);
      if (filter2) {
        (_b2 = me._filters) == null ? void 0 : _b2.remove(filter2);
        me._filtersFunction = null;
        if (!silent) {
          if (me.remoteFilter) {
            return me.filter().then(() => filter2);
          } else {
            me.filter();
          }
        }
        return filter2;
      }
    }
    /**
     * Removes all filters from the store.
     * @param {Boolean} [silent] Pass `true` to *not* refilter the store immediately.
     * @returns {Promise|null} If {@link Core/data/AjaxStore#config-filterParamName} is set on store, this method returns `Promise`
     * which is resolved after data is loaded from remote server, otherwise it returns `null`
     * @async
     * @category Sort, group & filter
     */
    clearFilters(silent = false) {
      this.filters.remove(this.filters.values.filter((f) => !f.internal));
      if (!silent) {
        return this.filter();
      }
    }
    convertFilterToString(field) {
      const filter2 = this.filters.getBy("property", field);
      return filter2 && !filter2.filterBy ? String(filter2) : "";
    }
    doDestroy() {
      var _a6;
      (_a6 = this._filters) == null ? void 0 : _a6.destroy();
      super.doDestroy();
    }
  }, __publicField(_a5, "$name", "StoreFilter"), //region Config
  __publicField(_a5, "configurable", {
    /**
     * Set this to `true` to activate remote filtering in this Store. This makes it possible to use the built-in
     * filtering features of the Store and corresponding UI functionality, without using local data.
     *
     * For a non-{@link Core.data.AjaxStore}, data will be requested by the Store by calling a by the app
     * implemented {@link Core.data.Store#config-requestData} function. Data can also be provided by listening to
     * the {@link Core.data.Store#event-requestData} event, and updating the {@link Core.data.Store#config-data}
     * property with new data.
     *
     * For {@link Core.data.AjaxStore}, data will be loaded via the configured
     * {@link Core.data.AjaxStore#config-readUrl}.
     *
     * @config {Boolean}
     * @category Filtering
     */
    remoteFilter: false,
    /**
     * The name of the parameter to use to pass any filters when loading data remotely.
     *
     * **Note:** When this is set, filters must be defined using a field name, an operator and a value
     * to compare, **not** a comparison function.
     * @config {String}
     * @category Remote
     */
    filterParamName: null
  }), __publicField(_a5, "properties", {
    filterBatching: 0,
    needsFiltering: false
  }), _a5;
};

// ../Core/lib/Core/data/mixin/StoreGroup.js
var resortActions = {
  add: 1,
  replace: 1
};
var NO_VALUE = "!!novalue!!";
var defaultGroupSortFn = ({ _groupValue: lhs }, { _groupValue: rhs }) => lhs < rhs ? -1 : lhs > rhs ? 1 : 0;
var arrayAt = (array, index) => array[index < 0 ? array.length + index : index];
var StoreGroup_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    static get properties() {
      return {
        collapsedGroups: /* @__PURE__ */ new Set()
      };
    }
    //endregion
    //region Init
    construct(config) {
      super.construct(config);
      this.ion({ change: "onDataChanged", thisObj: this });
    }
    get GroupRow() {
      var _a6;
      return this._GroupRowClass || (this._GroupRowClass = (_a6 = class extends this.modelClass {
        get isCollapsed() {
          return this.meta.collapsed;
        }
      }, __publicField(_a6, "isSpecialRow", true), // The calculated group value for this group header/footer.
      __publicField(_a6, "fields", [{
        name: "_groupValue"
      }]), _a6));
    }
    updateGroupers(groupers) {
      this.setGroupers(groupers);
    }
    /**
     * Set groupers.
     * @param {Grouper[]} groupers Array of groupers to apply to store
     * @returns {Promise|null} If {@link Core/data/AjaxStore#config-sortParamName} is set on store, this method returns
     * `Promise` which is resolved after data is loaded from remote server, otherwise it returns `null`
     * @async
     * @category Sort, group & filter
     * @non-lazy-load
     */
    setGroupers(groupers, options = null) {
      const me = this, { storage } = me;
      let result;
      me._idMap = null;
      if (groupers == null ? void 0 : groupers.length) {
        me._groupers = groupers.map((g) => me.createGrouper(g));
      } else if (me.groupers) {
        delete me._groupers;
        me.includeCollapsed();
        storage.replaceValues({
          values: me.removeHeadersAndFooters(storage._values),
          filteredValues: storage.isFiltered ? me.removeHeadersAndFooters(storage._filteredValues) : null,
          silent: true
        });
        result = me.group(null, null, null, false, options == null ? void 0 : options.silent);
      }
      return result;
    }
    createGrouper(grouper, ascending2) {
      if (!grouper || grouper.isGrouper) {
        return grouper;
      }
      if (typeof grouper === "string") {
        grouper = {
          field: grouper,
          [typeof ascending2 === "boolean" ? "ascending" : void 0]: ascending2
        };
      }
      const { field } = grouper, dotPos = field == null ? void 0 : field.indexOf("."), complexMapping = dotPos > 0, path = field == null ? void 0 : field.split("."), rootFieldName = complexMapping ? path[0] : field, leafFieldName = complexMapping ? arrayAt(path, -1) : field, fieldPath = complexMapping ? field.slice(dotPos + 1) : field;
      return {
        isGrouper: true,
        ...grouper,
        complexMapping,
        rootFieldName,
        leafFieldName,
        fieldPath
      };
    }
    get unfilteredGroupRecords() {
      var _a6;
      const me = this;
      if (me.isGrouped) {
        const { generation } = me.storage;
        if (((_a6 = me._unfilteredGroupRecords) == null ? void 0 : _a6.generation) !== generation) {
          me._unfilteredGroupRecords = me.storage.allValues.filter((r) => r.isSpecialRow);
          me._unfilteredGroupRecords.generation = generation;
        }
      }
      return me._unfilteredGroupRecords || [];
    }
    /**
     * Returns group header record for the passed record or last group header in the store
     * @param {Core.data.Model} [targetRecord]
     * @param {Boolean} [ignoreFilter] Pass true to search in the complete collection
     * @returns {Core.data.Model}
     * @internal
     */
    getGroupHeaderForRecord(targetRecord, ignoreFilter = false) {
      if (this.isGrouped) {
        return targetRecord ? "groupRowFor" in targetRecord.meta ? targetRecord : targetRecord.instanceMeta(this).groupParent : this.groupRecords.last;
      }
    }
    // Temporarily include records from collapsed groups, for example prior to filtering
    includeCollapsed() {
      for (const groupId of this.collapsedGroups) {
        this.expand(this.getById(groupId), false);
      }
    }
    // Exclude records in collapsed groups, intended to be used after a call to includeCollapsed()
    excludeCollapsed() {
      for (const groupId of this.collapsedGroups) {
        this.collapse(this.getById(groupId));
      }
    }
    onDataChange({ source: storage, action, removed }) {
      var _a6;
      const me = this, { groupers } = me;
      if (groupers) {
        if (groupers.length) {
          if (action === "splice" || action === "move") {
            storage.replaceValues({
              ...me.prepareGroupRecords(),
              silent: true
            });
          }
        } else {
          storage.replaceValues({
            values: me.removeHeadersAndFooters(storage._values),
            filteredValues: storage.isFiltered ? me.removeHeadersAndFooters(storage._filteredValues) : null,
            silent: true
          });
        }
      }
      (_a6 = super.onDataChange) == null ? void 0 : _a6.call(this, ...arguments);
    }
    move(records, beforeRecord) {
      const me = this;
      let needRegroup;
      if (me.isGrouped && !me.tree) {
        let prevRecord = beforeRecord;
        if (beforeRecord == null ? void 0 : beforeRecord.isSpecialRow) {
          prevRecord = me.getPrev(beforeRecord, false, false);
          if (!prevRecord) {
            return;
          }
        }
        const targetGroupHeader = me.getGroupHeaderForRecord(prevRecord), groupField = me.groupers[0].field, { reapplyFilterOnUpdate } = me, isArrayField = me.modelClass.getFieldDefinition(groupField).type === "array";
        let newGroupValue = targetGroupHeader.meta.groupRowFor;
        me.reapplyFilterOnUpdate = false;
        me.beginBatch();
        records.forEach((record) => {
          if (isArrayField) {
            const currentValue = record.get(groupField) || [];
            if (currentValue.includes(newGroupValue)) {
              return;
            } else {
              const fromGroup = record.instanceMeta(this).groupParent, groupValue = targetGroupHeader._groupValue;
              ArrayHelper.remove(currentValue, fromGroup);
              newGroupValue = currentValue.slice();
              if (groupValue === NO_VALUE) {
                newGroupValue = void 0;
              } else if (Array.isArray(groupValue) && groupValue.length === 0) {
                newGroupValue = [];
              } else {
                newGroupValue.push(groupValue);
              }
              needRegroup = true;
            }
          }
          record.setValue(groupField, newGroupValue);
        });
        me.endBatch();
        if (isArrayField && !needRegroup) {
          return;
        }
        me.reapplyFilterOnUpdate = reapplyFilterOnUpdate;
        if (me.isFiltered && (beforeRecord == null ? void 0 : beforeRecord.isSpecialRow)) {
          const { unfilteredGroupRecords } = me, index = unfilteredGroupRecords.indexOf(targetGroupHeader);
          beforeRecord = unfilteredGroupRecords[index + 1];
        }
      }
      super.move(records, beforeRecord);
      if (needRegroup) {
        me.group(me.groupers[0]);
      }
    }
    // private function that collapses on the data level
    collapse(groupRecords) {
      let result = false;
      if (groupRecords) {
        groupRecords = ArrayHelper.asArray(groupRecords);
        groupRecords.forEach((groupRecord) => {
          if (groupRecord && !groupRecord.meta.collapsed) {
            this.excludeGroupRecords(groupRecord);
            groupRecord.meta.collapsed = true;
            this.collapsedGroups.add(groupRecord.id);
            this.trigger("toggleGroup", { groupRecord, collapse: true });
            result = true;
          }
        });
      }
      return result;
    }
    // private function that expands on the data level
    expand(groupRecords, updateMap = true) {
      let result = false;
      if (groupRecords) {
        groupRecords = ArrayHelper.asArray(groupRecords);
        groupRecords.forEach((groupRecord) => {
          if (groupRecord == null ? void 0 : groupRecord.meta.collapsed) {
            this.includeGroupRecords(groupRecord);
            groupRecord.meta.collapsed = false;
            updateMap && this.collapsedGroups.delete(groupRecord.id);
            updateMap && this.trigger("toggleGroup", { groupRecord, collapse: false });
            result = true;
          }
        });
      }
      return result;
    }
    removeHeadersAndFooters(records) {
      return records.filter((r) => {
        if (r == null ? void 0 : r.unfilteredGroupChildren) {
          r.groupChildren.length = r.unfilteredGroupChildren.length = r.meta.childCount = 0;
        }
        if ((r == null ? void 0 : r.isSpecialRow) || (r == null ? void 0 : r.isLinked) && r.instanceMeta(this).groupParent) {
          this.unregister(r);
          return false;
        }
        return true;
      });
    }
    prepareGroupRecords() {
      var _a6;
      const me = this, {
        GroupRow,
        isFiltered,
        reapplyFilterOnUpdate,
        groupRecords
      } = me, grouper = me.groupers[0], multiplier = grouper.ascending !== false ? 1 : -1, {
        field,
        fn: groupSortFn = defaultGroupSortFn,
        leafFieldName
      } = grouper, byFn = typeof field === "function", groupingById = field === me.modelClass.idField, newGroupRecords = new Collection(), visibleRecordsIds = me._groupVisibleRecordIds || [], isVisible2 = (record) => {
        const matchesFilter = !isFiltered || me.filtersFunction(record);
        return reapplyFilterOnUpdate ? matchesFilter : matchesFilter || visibleRecordsIds.includes(record.id);
      }, collapseAll = Boolean(me.startGroupsCollapsed && !(groupRecords == null ? void 0 : groupRecords.count));
      groupRecords == null ? void 0 : groupRecords.forEach((groupRec) => {
        if (groupRec.isCollapsed) {
          me.includeGroupRecords(groupRec);
        }
      });
      const records = me.removeHeadersAndFooters(me.storage._values);
      if (me.sorters.length) {
        records.sort(me.sorterFn);
      }
      if (isFiltered) {
        me.filtersFunction = null;
      }
      records.forEach((rec) => {
        const original = rec, v = byFn ? field(rec) : rec.getValue(field), groupValues = ArrayHelper.asArray(v == void 0 ? NO_VALUE : v), multiValue = Array.isArray(groupValues), emptyArray18 = multiValue && !groupValues.length, length = emptyArray18 ? 1 : groupValues.length;
        for (let i = 0; i < length; i++) {
          const groupValue = emptyArray18 ? [] : groupValues[i], id2 = `group-header-${typeof groupValue === "number" ? groupValue : StringHelper.createId(groupValue)}`;
          let groupRec = newGroupRecords.get(id2);
          if (!groupRec) {
            groupRec = (groupRecords == null ? void 0 : groupRecords.get(id2)) || new GroupRow({
              [me.modelClass.idField]: id2,
              [groupingById ? "" : leafFieldName]: groupValue,
              _groupValue: groupValue
            }, me, {
              collapsed: collapseAll,
              specialRow: true,
              groupRowFor: groupValue,
              groupField: leafFieldName,
              emptyArray: emptyArray18
            });
            newGroupRecords.add(groupRec);
            groupRec.meta.childCount = 0;
            groupRec.groupChildren = [];
            groupRec.unfilteredGroupChildren = [];
            groupRec.stores = [me];
          }
          me.register(groupRec);
          if (i > 0) {
            const links = original.groupLinks || (original.groupLinks = {});
            rec = links[groupValue] || (links[groupValue] = original.link());
            rec.stores = [me];
            me.register(rec);
          }
          rec.instanceMeta(me).groupParent = groupRec;
          if (isVisible2(rec)) {
            groupRec.groupChildren.push(rec);
            groupRec.meta.childCount++;
          }
          groupRec.unfilteredGroupChildren.push(rec);
        }
      });
      newGroupRecords._values.sort((lhs, rhs) => groupSortFn(lhs, rhs) * multiplier);
      if (me.useGroupFooters) {
        for (let index = 0, length = newGroupRecords.count; index < length; index++) {
          const groupRec = newGroupRecords.values[index], val = groupRec.meta.groupRowFor, id2 = `group-footer-${typeof val === "number" ? val : StringHelper.createId(val)}`, footer = me.getById(id2) || new GroupRow({
            [me.modelClass.idField]: id2,
            [groupingById ? "" : leafFieldName]: val,
            _groupValue: val
          }, me, {
            specialRow: true,
            groupFooterFor: val,
            groupRecord: groupRec
          });
          footer.stores = [me];
          me.register(footer);
          footer.groupChildren = groupRec.groupChildren;
          newGroupRecords.splice(++index, 0, footer);
          length++;
          groupRec.groupChildren.push(footer);
          groupRec.unfilteredGroupChildren.push(footer);
          groupRec.meta.childCount++;
        }
      }
      me._idMap = null;
      const result = {
        values: newGroupRecords.reduce((result2, groupRec) => {
          if (!groupRec.isGroupFooter) {
            result2.push(groupRec);
            if (!groupRec.meta.collapsed) {
              result2.push(...groupRec.unfilteredGroupChildren);
            }
          }
          return result2;
        }, false, [])
      };
      if (isFiltered) {
        result.filteredValues = result.values.filter(isVisible2);
      }
      (_a6 = me.groupRecords) == null ? void 0 : _a6.forEach((oldGroupRec) => {
        if (!newGroupRecords.includes(oldGroupRec)) {
          oldGroupRec.destroy();
        }
      });
      me.groupRecords = newGroupRecords;
      return result;
    }
    //endregion
    //region Group and ungroup
    /**
     * Is store currently grouped?
     * @property {Boolean}
     * @readonly
     * @category Sort, group & filter
     */
    get isGrouped() {
      var _a6;
      return Boolean((_a6 = this.groupers) == null ? void 0 : _a6.length);
    }
    /**
     * Group records, either by replacing current sorters or by adding to them.
     * A grouper can specify a **_custom sorting function_** which will be called with arguments (recordA, recordB).
     * Works in the same way as a standard array sorter, except that returning `null` triggers the stores
     * normal sorting routine. Grouped store **must** always be sorted by the same field.
     *
     * ```javascript
     * // simple grouper
     * store.group('city');
     *
     * // grouper as object, descending order
     * store.group({ field : 'city', ascending : false });
     *
     * // using custom sorting function
     * store.group({
     *     field : 'city',
     *     fn : (recordA, recordB) => {
     *         // apply custom logic, for example:
     *         return recordA.city.length < recordB.city.length ? -1 : 1;
     *     }
     * });
     * ```
     *
     * @param {String|Object} field Field to group by.
     * Can also be a config containing a field to group by and a custom sorting function called `fn`.
     * @param {String} field.field Field to group by
     * @param {Function} field.fn Custom sorting function
     * @param {Boolean} field.ascending  Sort order of the group titles
     * @param {Boolean} [ascending] Sort order of the group titles
     * @param {Boolean} [add] Add a grouper (true) or use only this grouper (false)
     * @param {Boolean} [performSort] Trigger sort directly, which does the actual grouping
     * @param {Boolean} [silent] Set as true to not fire events
     * @category Sort, group & filter
     * @fires group
     * @fires Core.data.Store#event-refresh
     * @returns {Promise|null} If {@link Core/data/AjaxStore#config-sortParamName} is set on store, this method returns `Promise`
     * which is resolved after data is loaded from remote server, otherwise it returns `null`
     * @async
     * @non-lazy-load
     */
    group(field, ascending2, add = false, performSort = true, silent = false) {
      var _a6, _b2;
      const me = this, { sorters = [] } = me, grouper = me.createGrouper(field, ascending2);
      let newGrouper, sortersModified = false;
      if (add) {
        me.groupers.push(grouper);
      } else if (field) {
        if (ascending2 == null && !("ascending" in grouper)) {
          grouper.ascending = grouper.fn && ((_a6 = me.groupInfo) == null ? void 0 : _a6.field) === grouper.field && ((_b2 = me.groupInfo) == null ? void 0 : _b2.fn) === grouper.fn ? !me.groupInfo.ascending : true;
        }
        me.groupInfo = newGrouper = grouper;
        me.groupers = [me.groupInfo];
        if (sorters.some((sorter) => sorter.field === field)) {
          me.sorters = sorters.filter((sorter) => sorter.field !== field);
          sortersModified = true;
        }
      } else if (field === null && ascending2 === null) {
        performSort = true;
        sortersModified = true;
      } else if (!sortersModified && sorters.length && performSort) {
        sortersModified = true;
      }
      if (newGrouper) {
        const { prototype: prototype2 } = me.modelClass;
        if (newGrouper.complexMapping && !Object.prototype.hasOwnProperty.call(prototype2, field)) {
          Object.defineProperty(prototype2, field, {
            get() {
              return ObjectHelper.getPath(this, field);
            }
          });
        }
      }
      if (performSort !== false) {
        if (me.remoteSort && !me.isRemoteDataLoading) {
          return me.sort(null, null, false, !sortersModified).then(() => me.onAfterGrouping(silent, !sortersModified));
        }
        me.sort(null, null, false, !sortersModified);
      }
      me.onAfterGrouping(silent, !sortersModified);
    }
    onAfterGrouping(silent, doRefresh = true) {
      if (silent) {
        return;
      }
      const me = this, groupers = me.groupers || [];
      me.trigger("group", { isGrouped: me.isGrouped, groupers, records: me.storage.values });
      if (doRefresh) {
        me.trigger("refresh", { action: "group", isGrouped: me.isGrouped, groupers, records: me.storage.values });
      }
    }
    // Internal since UI does not support multi grouping yet
    /**
     * Add a grouping level (a grouper).
     * @param {String} field Field to group by
     * @param {Boolean} ascending Group direction
     * @category Sort, group & filter
     * @returns {Promise|null} If {@link Core/data/AjaxStore#config-sortParamName} is set on store, this method returns `Promise`
     * which is resolved after data is loaded from remote server, otherwise it returns `null`
     * @async
     * @internal
     */
    addGrouper(field, ascending2 = true) {
      return this.group(field, ascending2, true);
    }
    // Internal since UI does not support multi grouping yet
    /**
     * Removes a grouping level (a grouper)
     * @param {String} field Grouper to remove
     * @category Sort, group & filter
     * @returns {Promise|null} If {@link Core/data/AjaxStore#config-sortParamName} is set on store, this method returns `Promise`
     * which is resolved after data is loaded from remote server, otherwise it returns `null`
     * @async
     * @internal
     */
    removeGrouper(field) {
      const me = this, { groupers } = me;
      if (!groupers) {
        return;
      }
      const index = groupers.findIndex((grouper) => grouper.field === field);
      if (index > -1) {
        groupers.splice(index, 1);
        if (!groupers.length) {
          return me.clearGroupers();
        } else {
          return me.group();
        }
      }
    }
    /**
     * Removes all groupers, turning store grouping off.
     * @privateparam {Boolean} [silent=false] Pass true to suppress events.
     * @returns {Promise|null} If {@link Core/data/AjaxStore#config-sortParamName} is set on store, this method returns `Promise`
     * which is resolved after data is loaded from remote server, otherwise it returns `null`
     * @async
     * @category Sort, group & filter
     * @non-lazy-load
     */
    clearGroupers(silent = false) {
      return this.setGroupers(null, { silent });
    }
    //endregion
    //region Get and check
    /**
     * Check if a record belongs to a certain group (only for the first grouping level)
     * @param {Core.data.Model} record The Record
     * @param {*} groupValue The group value
     * @returns {Boolean} True if the record belongs to the group, otherwise false
     * @category Sort, group & filter
     * @non-lazy-load
     */
    isRecordInGroup(record, groupValue) {
      var _a6;
      if (!this.isGrouped) {
        return false;
      }
      const groupField = (_a6 = this.groupers[0]) == null ? void 0 : _a6.field;
      return record.getValue(groupField) === groupValue && !record.isSpecialRow;
    }
    isInCollapsedGroup(record) {
      const { groupParent } = record.instanceMeta(this);
      return groupParent == null ? void 0 : groupParent.meta.collapsed;
    }
    /**
     * Returns all records in the group with specified groupValue.
     * @param {*} groupValue
     * @returns {Core.data.Model[]} Records in specified group or null if store not grouped
     * @category Sort, group & filter
     * @non-lazy-load
     */
    getGroupRecords(groupValue) {
      if (!this.isGrouped) {
        return null;
      }
      return this.storage.values.filter((record) => this.isRecordInGroup(record, groupValue));
    }
    /**
     * Get all group titles.
     * @returns {String[]} Group titles
     * @category Sort, group & filter
     * @non-lazy-load
     */
    getGroupTitles() {
      if (!this.isGrouped) {
        return null;
      }
      return this.getDistinctValues(this.groupers[0].field);
    }
    /**
     * Returns all the group header records
     * @member {Core.data.Model[]} groupHeaderRecords The records representing the group header rows
     * @readonly
     * @non-lazy-load
     */
    get groupHeaderRecords() {
      if (this.useGroupFooters) {
        return this.groupRecords.values.filter((r) => r.isGroupHeader);
      }
      return this.groupRecords.values;
    }
    //endregion
    onDataChanged({ changes, action }) {
      if (this.isGrouped && // If an action flagged as requiring resort is performed...
      (!changes && resortActions[action] || // ...or if the group field has changes...
      changes && this.groupers.some((grouper) => grouper.rootFieldName in changes))) {
        this.sort();
      }
    }
    /**
     * Adds or removes records in a group from storage. Used when expanding/collapsing groups.
     * @private
     * @param {Core.data.Model} groupRecord Group which records should be added or removed
     * @param {Boolean} include Include (true) or exclude (false) records
     * @category Grouping
     */
    internalIncludeExcludeGroupRecords(groupRecord, include) {
      const me = this, index = me.indexOf(groupRecord), allIndex = me.allIndexOf(groupRecord), { id: mapId, storage } = me, {
        _filteredValues,
        _values
      } = storage, {
        meta,
        groupChildren,
        unfilteredGroupChildren
      } = groupRecord;
      if (allIndex === -1 || meta.collapsed && !include || !meta.collapsed && include) {
        return;
      }
      unfilteredGroupChildren.forEach(
        (child) => child.instanceMeta(mapId).hiddenByCollapse = !include
      );
      if (include) {
        if (_filteredValues) {
          _filteredValues.splice(index + 1, 0, ...groupChildren.filter((r) => !me.isAvailable(r.id)));
        }
        storage._values.splice(allIndex + 1, 0, ...unfilteredGroupChildren.filter((r) => !me.isAvailable(r.id)));
      } else {
        if (_filteredValues) {
          _filteredValues.splice(index + 1, groupChildren.length);
        }
        _values.splice(allIndex + 1, unfilteredGroupChildren.length);
      }
      storage._indicesInvalid = true;
      me._idMap = null;
    }
    /**
     * Removes records in a group from storage. Used when collapsing a group.
     * @private
     * @param groupRecord Group which records should be removed
     * @category Grouping
     */
    excludeGroupRecords(groupRecord) {
      this.internalIncludeExcludeGroupRecords(groupRecord, false);
    }
    /**
     * Adds records in a group to storage. Used when expanding a group.
     * @private
     * @param groupRecord Group which records should be added
     * @category Grouping
     */
    includeGroupRecords(groupRecord) {
      this.internalIncludeExcludeGroupRecords(groupRecord, true);
    }
    /**
     * Collects all group headers + children, whether expanded or not
     * @private
     * @param {Boolean} allRecords True to include filtered out records
     * @param {Boolean} includeHeaders True to also include group headers
     * @returns {Core.data.Model[]}
     */
    collectGroupRecords(allRecords, includeHeaders = true) {
      const records = allRecords ? this.storage.allValues : this.storage.values;
      return records.reduce((records2, record) => {
        if (record.isSpecialRow) {
          if (includeHeaders && !record.isGroupFooter) {
            records2.push(record);
          }
          if (record.isGroupHeader) {
            records2.push.apply(records2, allRecords ? record.unfilteredGroupChildren : record.groupChildren);
          }
        }
        return records2;
      }, []);
    }
  }, __publicField(_a5, "$name", "StoreGroup"), //region Config
  __publicField(_a5, "configurable", {
    /**
     * Currently used groupers.
     * To set groupers when remote sorting is enabled by {@link Core/data/AjaxStore#config-sortParamName} you should
     * use {@link #function-setGroupers} instead to be able to wait for the operation to finish.
     * @member {Grouper[]} groupers
     * @category Sort, group & filter
     */
    /**
     * Initial groupers, specify to have store grouped automatically after initially setting data
     * @config {Grouper[]}
     * @category Common
     * @non-lazy-load
     */
    groupers: null,
    useGroupFooters: false,
    /**
     * To have all groups __initially loaded__ start collapsed, configure this as `true`.
     *
     * Note that this only affects the initial load of the store. Subsequent reloads maintain
     * current group state where possible.
     * @config {Boolean}
     * @default false
     * @category Advanced
     */
    startGroupsCollapsed: null
  }), _a5;
};

// ../Core/lib/Core/data/mixin/StorePaging.js
var immediatePromise2 = Promise.resolve();
var StorePaging_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    get currentPage() {
      var _a6;
      return (_a6 = this._currentPage) != null ? _a6 : 1;
    }
    set currentPage(page) {
      if (this.totalCount) {
        page = Math.min(page, this.lastPage);
      }
      this._currentPage = page;
    }
    updatePageParamName(param) {
      if (this.pageStartParamName) {
        throw new Error("Configs pageStartParamName and pageParamName are mutually exclusive");
      }
      this.remotePaging = param != null;
    }
    updatePageStartParamName(param) {
      if (this.pageParamName) {
        throw new Error("Configs pageParamName and pageStartParamName are mutually exclusive");
      }
      this.remotePaging = param != null;
    }
    get defaultPageParamName() {
      var _a6, _b2;
      return (_b2 = (_a6 = this.pageParamName) != null ? _a6 : this.pageStartParamName) != null ? _b2 : "page";
    }
    updateRemotePaging(paging) {
      if (paging && this.tree) {
        throw new Error("Paging cannot be supported for tree stores");
      }
    }
    // This function is called to automatically reload page whenever pageSize is set at runtime.
    updatePageSize(pageSize) {
      const me = this;
      if (!me.isConfiguring && pageSize) {
        me.currentPage = me.currentPage ? Math.min(me.currentPage, Math.floor((me.totalCount + pageSize - 1) / pageSize)) : 1;
        me.loadPage(me.currentPage);
      }
    }
    /**
     * Yields `true` if this Store is loaded page by page. See the {@link #config-remotePaging} config.
     * @property {Boolean}
     * @readonly
     * @category Paging
     * @non-lazy-load
     */
    get isPaged() {
      return this.remotePaging;
    }
    set pagedTotal(count) {
      this.remoteTotal = count;
    }
    /**
     * **If the store {@link #property-isPaged is paged}**, yields the highest page number in the dataset as calculated
     * from the 'total' returned in the last page data block loaded.
     * @property {Number}
     * @readonly
     * @category Paging
     * @non-lazy-load
     */
    get lastPage() {
      if (this.isPaged) {
        return Math.floor((this.totalCount + this.pageSize - 1) / this.pageSize);
      }
    }
    // region Events
    /**
     * When the store {@link #property-isPaged is paged}, this is fired before loading a page and is cancelable
     * @event beforeLoadPage
     * @preventable
     * @param {Core.data.Store} source This Store
     * @param {String} action (AjaxStore only) The read action being performed: `'readPage'`
     * @param {String} url (AjaxStore only) The URL to which the HTTP request will be sent. This property may be mutated in
     * an event handler *without changing* the base {@link Core.data.AjaxStore#config-readUrl} configured for this Store.
     * @param {Object} params An object containing property/name pairs which are the parameters.
     * This may be mutated to affect the parameters used in the request.
     */
    /**
     * When the store {@link #property-isPaged is paged}, this is fired when a page is loaded.
     * @event loadPage
     * @param {Core.data.Store} source This Store
     * @param {Object} params An object containing property/name pairs which are the parameters.
     * This may be mutated to affect the parameters used in the request.
     */
    // endregion
    /**
     * Loads a page of data from the implemented {@link Core.data.Store#config-requestData} function.
     * @param {Number} page The *one based* page number to load.
     * @param {Object} params A hash of parameters to append to the request event and requestData call
     * @returns {Promise} A Promise which will be resolved when the request completes
     * @category CRUD
     * @non-lazy-load
     * @async
     */
    async loadPage(page, params = {}) {
      var _a6;
      params = this.getPagingParams({ ...params, page });
      if (await this.trigger("beforeLoadPage", { params }) === false) {
        return false;
      }
      if (await this.performDataRequest(false, params, () => {
        this.currentPage = page;
      }) !== false) {
        (_a6 = this.trigger) == null ? void 0 : _a6.call(this, "loadPage", params);
      }
    }
    /**
     * If this store {@link #property-isPaged is paged}, and is not already at the {@link #property-lastPage}
     * then this will load the next page of data.
     * @category CRUD
     * @returns {Promise} A promise which is resolved when the request completes and has been processed.
     * @non-lazy-load
     */
    async nextPage(params) {
      const me = this;
      return me.isPaged && me.currentPage !== me.lastPage ? me.loadPage(me.currentPage + 1, params) : immediatePromise2;
    }
    /**
     * If this store {@link #property-isPaged is paged}, and is not already at the first page
     * then this will load the previous page of data.
     * @category CRUD
     * @returns {Promise} A promise which is resolved when the request completes and has been processed.
     * @non-lazy-load
     */
    async previousPage(params) {
      return this.isPaged && this.currentPage !== 1 ? this.loadPage(this.currentPage - 1, params) : immediatePromise2;
    }
    buildRemoteParams(params = {}, encode = false) {
      var _a6, _b2;
      return this.getPagingParams((_b2 = (_a6 = super.buildRemoteParams) == null ? void 0 : _a6.call(this, params, encode)) != null ? _b2 : params);
    }
    getPagingParams(params) {
      var _a6;
      const me = this;
      if (me.isPaged) {
        let currentPage = (_a6 = params[me.defaultPageParamName]) != null ? _a6 : me.currentPage;
        if (this.pageStartParamName) {
          currentPage = Math.max(0, currentPage - 1) * this.pageSize;
        }
        params[me.defaultPageParamName] = currentPage;
        params[me.pageSizeParamName] = me.pageSize;
      }
      return params;
    }
  }, __publicField(_a5, "$name", "StorePaging"), __publicField(_a5, "configurable", {
    /**
     * When the Store is paged by configuring {@link #config-remotePaging}, this value will be included in the load
     * requests.
     *
     * **Note:** Setting pageSize at runtime will automatically reload the page.
     * @prp {Number}
     * @default
     * @category Paging
     * @non-lazy-load
     */
    pageSize: 50,
    /**
     * The name of the parameter to use when requesting pages of data, representing the configured
     * {@link #config-pageSize} value.
     * @config {String}
     * @default
     * @category Paging
     * @non-lazy-load
     */
    pageSizeParamName: "pageSize",
    /**
     * The name of the parameter to use when requesting pages of data using the **zero based** index of the required
     * page's starting record.
     * @config {String}
     * @category Paging
     * @non-lazy-load
     */
    pageParamName: null,
    /**
     * The name of the parameter to use when requesting pages of data using the **one based** page number required.
     * @config {String}
     * @category Paging
     * @non-lazy-load
     */
    pageStartParamName: null,
    /**
     * Set this to `true` to activate remote paging in this Store. Makes it possible to use the
     * {@link #function-loadPage}, {@link #function-nextPage}, and {@link #function-previousPage} functions. Or
     * add the {@link Core.widget.PagingToolbar} to control what page to load.
     *
     * For a non-{@link Core.data.AjaxStore}, data will be requested by the Store by calling a by the app
     * implemented {@link Core.data.Store#config-requestData} function. Data can also be provided by listening to
     * the {@link Core.data.Store#event-requestData} event, and updating the {@link Core.data.Store#config-data}
     * property with new data.
     *
     * @config {Boolean}
     * @category Paging
     */
    remotePaging: false
  }), _a5;
};

// ../Core/lib/Core/data/mixin/StoreProxy.js
var StoreProxy_default = (Target) => class StoreProxy extends (Target || Base) {
  static get configurable() {
    return {
      /**
       * Allow object like interaction with the Store. For example:
       *
       * ```javascript
       * const store = new Store({
       *    objectify : true,
       *    data      : [
       *        { id : 'batman', name : 'Bruce' }
       *    ]
       * });
       *
       * // retrieve using id as property
       * const record = store.batman;
       *
       * // add as property
       * store.superman = { name : 'Clark' };
       *
       * // delete to remove
       * delete store.batman;
       * ```
       *
       * @config {Boolean}
       * @default false
       */
      objectify: null
    };
  }
  initProxy() {
    if (!globalThis.Proxy) {
      throw new Error("Proxy not supported");
    }
    const proxy = new Proxy(this, {
      // Support getting records using `store[id/index]
      get(target, property) {
        if (property in target) {
          return target[property];
        }
        if (property === "$store") {
          return target;
        }
        let record = target.getById(property);
        if (!record && !isNaN(parseInt(property))) {
          record = target.getAt(parseInt(property));
        }
        return record;
      },
      // Support adding/replacing records using `store.id = { ...data }`
      set(target, property, value2) {
        if (property in target || target.isDestroying || property.startsWith("_")) {
          target[property] = value2;
        } else {
          target.add({ [target.modelClass.idField]: property, ...value2 });
        }
        return true;
      },
      // Support deleting records using `delete store.id`
      deleteProperty(target, property) {
        if (target.isDestroying) {
          delete target[property];
          return true;
        }
        return Boolean(target.remove(property).length);
      },
      // Support `id in store`
      has(target, property) {
        if (property in target) {
          return true;
        }
        if (property.startsWith("{") && property.endsWith("}")) {
          const data = StringHelper.safeJsonParse(property);
          property = data == null ? void 0 : data.id;
        }
        return target.includes(property);
      }
    });
    return proxy;
  }
};

// ../Core/lib/Core/data/mixin/StoreRelation.js
var StoreRelation_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    //region Init
    /**
     * Initialized relations, called from constructor
     * @private
     */
    initRelations(reset) {
      const me = this, relations = me.modelClass.exposedRelations;
      if (reset && me.modelRelations) {
        me.modelRelations.forEach((relation) => {
          var _a6;
          return (_a6 = relation.storeDetacher) == null ? void 0 : _a6.call(relation);
        });
      }
      if ((!me.modelRelations || me.modelRelations.length === 0 || reset) && !me.isChained) {
        me.modelRelations = [];
        relations == null ? void 0 : relations.forEach((modelRelationConfig) => {
          const config = { ...modelRelationConfig }, {
            foreignStore,
            relationName,
            relatedCollectionName
          } = config, relatedStore = typeof foreignStore === "string" ? me[foreignStore] : foreignStore;
          config.dependentStore = me;
          me.modelRelations.push(config);
          if (relatedStore) {
            config.foreignStoreProperty = config.foreignStore;
            config.foreignStore = relatedStore;
            const dependentStoreConfigs = relatedStore.dependentStoreConfigs;
            if (dependentStoreConfigs.has(me)) {
              const dependentConfigs = dependentStoreConfigs.get(me);
              if (reset) {
                const existingConfig = dependentConfigs.find((c) => c.relationName === relationName);
                if (existingConfig) {
                  ArrayHelper.remove(dependentConfigs, existingConfig);
                }
              }
              dependentConfigs.push(config);
            } else {
              dependentStoreConfigs.set(me, [config]);
            }
            if (relatedCollectionName) {
              relatedStore.initRelationCollection(config, me);
            }
            if (relatedStore.count > 0) {
              relatedStore.updateDependentStores("dataset", relatedStore.records);
            }
          }
        });
      }
    }
    /**
     * Called from other end of a relation when this store should hold a collection of related records.
     * @private
     * @param config
     * @param collectionStore
     */
    initRelationCollection(config, collectionStore) {
      const me = this, name = config.relatedCollectionName, collectionStores = me.collectionStores || (me.collectionStores = {});
      collectionStores[name] = {
        store: collectionStore,
        config
      };
      if (!me[name + "Store"]) {
        me[name + "Store"] = collectionStore;
      }
      if (me.allRecords.length) {
        me.initModelRelationCollection(name, me.allRecords);
      }
    }
    initModelRelationCollection(name, records) {
      const me = this;
      records.forEach((record) => {
        record.traverse((node) => {
          const useName = name in node ? `$related${StringHelper.capitalize(name)}` : name;
          Object.defineProperty(node, useName, {
            enumerable: true,
            configurable: true,
            get: function() {
              return me.getCollection(this, name);
            },
            set: function(value2) {
              return me.setCollection(this, name, value2);
            }
          });
        });
      });
    }
    /**
     * Updates relationCache for all records.
     * @private
     */
    resetRelationCache() {
      this.relationCache = {};
      this.forEach((record) => record.initRelations());
    }
    /**
     * Caches related records from related store on the local store.
     * @private
     * @param record Local record
     * @param relations Relations to related store
     */
    updateRecordRelationCache(record, relations) {
      relations == null ? void 0 : relations.forEach((relation) => {
        const { config } = relation, foreignId = relation.related ? relation.related.id : record.getValue(config.foreignKey);
        foreignId !== void 0 && this.cacheRelatedRecord(record, foreignId, config.relationName, foreignId);
      });
    }
    //endregion
    //region Getters
    /**
     * Returns records the relation cache. Same result as if retrieving the collection on the dependent store, but
     * without the need of accessing that store.
     * @internal
     * @param {String} name
     * @param {Core.data.Model|String|Number} recordOrId
     * @returns {Array}
     */
    getRelationCollection(name, recordOrId) {
      var _a6;
      const id2 = Model.asId(recordOrId);
      return ((_a6 = this.relationCache[name]) == null ? void 0 : _a6[id2]) || [];
    }
    /**
     * Returns records from a collection of related records. Not to be called directly, called from Model getter.
     * @private
     * @param {Core.data.Model} record
     * @param {String} name
     * @returns {Array}
     */
    getCollection(record, name) {
      var _a6;
      const { config, store } = this.collectionStores[name];
      return ((_a6 = store.relationCache[config.relationName]) == null ? void 0 : _a6[record.id]) || [];
    }
    /**
     * Sets a collection of related records. Will updated the related store and trigger events from it. Not to be called
     * directly, called from Model setter.
     * @private
     */
    setCollection(model, name, records) {
      const { config, store } = this.collectionStores[name], relationCache = store.relationCache[config.relationName] || (store.relationCache[config.relationName] = {}), old = (relationCache[model.id] || []).slice(), added = [], removed = [];
      store.suspendEvents();
      old.forEach((record) => {
        if (!records.includes(record)) {
          record[config.foreignKey] = null;
          store.remove(record);
          removed.push(record);
        }
      });
      records.forEach((record) => {
        if (record.isModel instanceof Model) {
          if (!record.stores.includes(store)) {
            store.add(record);
            added.push(record);
          }
        } else {
          [record] = store.add(record);
          added.push(record);
        }
        record[config.foreignKey] = model.id;
      });
      store.resumeEvents();
      if (removed.length) {
        store.trigger("remove", { records: removed });
        store.trigger("change", { action: "remove", records: removed });
      }
      if (added.length) {
        store.trigger("add", { records: added });
        store.trigger("change", { action: "add", records: added });
      }
    }
    //endregion
    //region Caching
    /**
     * Adds a record to relation cache, optionally removing it if already there.
     * @private
     * @param record
     * @param id
     * @param name
     * @param uncacheId
     */
    cacheRelatedRecord(record, id2, name, uncacheId = null) {
      const me = this, cache = me.relationCache[name] || (me.relationCache[name] = {});
      if (uncacheId !== null) {
        me.uncacheRelatedRecord(record, name, uncacheId);
      }
      if (id2 != null) {
        ArrayHelper.include(cache[id2] || (cache[id2] = []), record);
      }
    }
    /**
     * Removes a record from relation cache, for a specific relation (specify relation name and id) or for all relations
     * @private
     * @param record Record to remove from cache
     * @param name Optional, relation name
     * @param id Optional, id
     */
    uncacheRelatedRecord(record, name = null, id2 = null) {
      const me = this;
      function remove(relationName, relatedId) {
        const cache = me.relationCache[relationName], oldCache = cache == null ? void 0 : cache[relatedId];
        if (oldCache) {
          const uncacheIndex = oldCache.indexOf(record);
          uncacheIndex >= 0 && oldCache.splice(uncacheIndex, 1);
          if (oldCache.length === 0) {
            delete cache[relatedId];
          }
        }
      }
      if (id2 != null) {
        remove(name, id2);
      } else {
        if (record.meta.relationCache) {
          Object.entries(record.meta.relationCache).forEach(
            ([relationName, relatedRecord]) => remove(relationName, relatedRecord == null ? void 0 : relatedRecord.id)
          );
        }
      }
    }
    /**
     * Updates related stores when store is cleared, a record is removed or added.
     * @private
     * @param {String} action
     * @param {Core.data.Model[]} records
     */
    updateDependentStores(action, records, silent) {
      var _a6;
      const me = this, isDataset = action === "dataset";
      me.dependentStoreConfigs.forEach((configs) => {
        configs.forEach((config) => {
          const {
            dependentStore,
            relatedCollectionName,
            relationName,
            foreignKey,
            propagateRecordChanges
          } = config, cache = dependentStore.relationCache[relationName];
          if (isDataset) {
            relatedCollectionName && me.initModelRelationCollection(relatedCollectionName, records);
            dependentStore.forEach((record) => {
              const foreign = record.initRelation(config);
              foreign && dependentStore.cacheRelatedRecord(record, foreign.id, relationName, foreign.id);
            });
            if (propagateRecordChanges && dependentStore.totalCount) {
              dependentStore.trigger("refresh", { action: "relatedstoredataset", records: dependentStore.records });
            }
          } else if (action === "removeall") {
            dependentStore.forEach((record) => record.removeRelation(config));
            delete dependentStore.relationCache[relationName];
            return;
          } else if (action === "replace") {
            records = records.map((r) => r[1]);
          }
          if (action === "add" || action === "replace") {
            relatedCollectionName && me.initModelRelationCollection(relatedCollectionName, records);
          }
          if (!isDataset) {
            records.forEach((record) => {
              const dependentRecords = cache == null ? void 0 : cache[record.id];
              switch (action) {
                case "remove":
                  if (dependentRecords) {
                    dependentRecords.forEach((dependentRecord) => dependentRecord.removeRelation(config));
                  }
                  break;
                case "add":
                case "replace":
                  dependentStore.forEach((dependentRecord) => {
                    if (dependentRecord.getValue(foreignKey) == record.id) {
                      dependentRecord.initRelation(config);
                      dependentStore.cacheRelatedRecord(dependentRecord, record.id, relationName);
                    }
                  });
                  break;
              }
              if (!silent && (dependentRecords == null ? void 0 : dependentRecords.length) && propagateRecordChanges) {
                dependentRecords.forEach(
                  (dependentRecord) => dependentRecord.stores.forEach((store) => me.notifyRelatedStore(store, dependentRecord, config.relationName, record))
                );
              }
            });
          }
        });
      });
      if (records == null ? void 0 : records.length) {
        (_a6 = me.modelRelations) == null ? void 0 : _a6.forEach((relation) => {
          if (relation.propagateRecordChanges) {
            if (!isDataset) {
              records.forEach((record) => {
                var _a7;
                const relatedRecord = record[relation.relationName];
                (_a7 = relatedRecord == null ? void 0 : relatedRecord.stores) == null ? void 0 : _a7.forEach((store) => {
                  me.notifyRelatedStore(store, relatedRecord, relation.relatedCollectionName, record);
                });
              });
            } else if (relation.foreignStore.totalCount) {
              relation.foreignStore.trigger("refresh", {
                action: "relatedstoredataset",
                records: relation.foreignStore.records
              });
            }
          }
        });
      }
    }
    notifyRelatedStore(store, relatedRecord, relationName, { meta }) {
      if (this.isChained || store.isChained) {
        return;
      }
      let { _ignoreRelationUpdate } = meta, isInitiator = false;
      if (!(_ignoreRelationUpdate == null ? void 0 : _ignoreRelationUpdate.size)) {
        meta._ignoreRelationUpdate = _ignoreRelationUpdate = /* @__PURE__ */ new Set();
        isInitiator = true;
      }
      if (!_ignoreRelationUpdate.has(store)) {
        _ignoreRelationUpdate.add(this).add(store);
        relatedRecord.meta._ignoreRelationUpdate = _ignoreRelationUpdate;
        store.onModelChange(relatedRecord, { [relationName]: relatedRecord[relationName] }, { [relationName]: relatedRecord[relationName] }, false, true);
        if (isInitiator) {
          _ignoreRelationUpdate.clear();
        }
      }
    }
    /**
     * Updates relation cache and foreign key value when a related objects id is changed.
     * @private
     */
    updateDependentRecordIds(oldValue, value2) {
      var _a6;
      (_a6 = this.dependentStoreConfigs) == null ? void 0 : _a6.forEach((configs) => {
        configs.forEach((config) => {
          var _a7;
          const {
            dependentStore,
            relationName,
            foreignKey
          } = config, cache = dependentStore.relationCache[relationName], localRecords = (_a7 = cache == null ? void 0 : cache[oldValue]) == null ? void 0 : _a7.slice();
          localRecords == null ? void 0 : localRecords.forEach((localRecord) => {
            dependentStore.cacheRelatedRecord(localRecord, value2, relationName, oldValue);
            localRecord.meta._ignoreRelatedIdUpdate = true;
            localRecord.set(foreignKey, value2, false, true);
            delete localRecord.meta._ignoreRelatedIdUpdate;
          });
        });
      });
    }
    //endregion
  }, __publicField(_a5, "$name", "StoreRelation"), _a5;
};

// ../Core/lib/Core/data/mixin/StoreSum.js
var StoreSum_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    /**
     * Returns sum calculated by adding value of specified field for specified records. Defaults to using all locally
     * available records in store
     * @param {String} field Field to summarize by
     * @param {Core.data.Model[]} [records] Records to summarize, uses all records if unspecified.
     * @returns {Number}
     * @category Sum
     */
    sum(field, records = this.storage.values) {
      if (!records) {
        return 0;
      }
      return records.reduce((sum, record) => {
        if (record.isSpecialRow) {
          return sum;
        }
        const v = Number(record.getValue(field));
        return isNaN(v) ? sum : sum + v;
      }, 0);
    }
    /**
     * Returns min value for the specified field, can be used with Date or Number values. Defaults to look through all
     * locally available records in store
     * @param {String} field Field to find min value for
     * @param {Core.data.Model[]} [records] Records to process, uses all records if unspecified
     * @returns {Number|Date}
     * @category Sum
     */
    min(field, records = this.storage.values) {
      if (!(records == null ? void 0 : records.length)) {
        return 0;
      }
      return records.reduce((min4, record) => {
        const fieldValue = record.getValue(field), type = typeof (fieldValue == null ? void 0 : fieldValue.valueOf());
        if (type === "number" && (fieldValue < min4 || min4 == null)) {
          min4 = fieldValue;
        }
        return min4;
      }, records[0].getValue(field));
    }
    /**
     * Returns max value for the specified field, can be used with Date or Number values. Defaults to look through all
     * locally available records in store
     * @param {String} field Field to find max value for
     * @param {Core.data.Model[]} [records] Records to process, uses all records if unspecified
     * @returns {Number|Date}
     * @category Sum
     */
    max(field, records = this.storage.values) {
      if (!(records == null ? void 0 : records.length)) {
        return 0;
      }
      return records.reduce((max3, record) => {
        const fieldValue = record.getValue(field), type = typeof (fieldValue == null ? void 0 : fieldValue.valueOf());
        if (type === "number" && (fieldValue > max3 || max3 == null)) {
          max3 = fieldValue;
        }
        return max3;
      }, records[0].getValue(field));
    }
    /**
     * Returns the average value for the specified field. Defaults to look through all locally available records in
     * store
     * @param {String} field Field to calculate average value for
     * @param {Core.data.Model[]} [records] Records to process, uses all records if unspecified
     * @returns {Number}
     * @category Sum
     */
    average(field, records = this.storage.values) {
      if (!(records == null ? void 0 : records.length)) {
        return 0;
      }
      let count = 0;
      const sum = records.reduce((sum2, record) => {
        if (record.isSpecialRow) {
          return sum2;
        }
        const v = parseFloat(record.getValue(field));
        if (!isNaN(v)) {
          count++;
          return sum2 + v;
        } else {
          return sum2;
        }
      }, 0);
      return count > 0 ? sum / count : 0;
    }
    /**
     * Returns sum by adding value of specified field for records in the group with the specified groupValue.
     * @param {*} groupValue The group to summarize
     * @param {String} field Field to summarize by
     * @returns {Number} Sum or null if store not grouped
     * @category Sum
     * @non-lazy-load
     */
    groupSum(groupValue, field) {
      return this.sum(field, this.getGroupRecords(groupValue));
    }
  }, __publicField(_a5, "$name", "StoreSum"), _a5;
};

// ../Core/lib/Core/data/mixin/StoreSearch.js
var findInString = (value2, text) => String(value2).toLowerCase().includes(text);
var matchFns = {
  string: findInString,
  number: (value2, text) => value2 === Number(text),
  boolean: findInString,
  date: (value2, text) => {
    if (value2 instanceof Date && text instanceof Date) {
      return value2 - text === 0;
    }
    return String(value2.getMonth() + 1).includes(text) || String(value2.getDate()).includes(text) || String(value2.getFullYear()).includes(text);
  },
  object: (value2, text) => value2 === text,
  // typeof null === object
  undefined: (value2, text) => value2 === text
};
var StoreSearch_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    //region Search (multiple hits)
    /**
     * Find all hits matching the specified input.
     *
     * ```javascript
     * const store = new Store({
     *   fields : ['name', 'kind'],
     *   data : [
     *     { id : 1, name : 'Batman', kind : 'hero' },
     *     { id : 2, name : 'Batkid', kind : 'hero' },
     *     { id : 3, name : 'Joker', kind : 'villain' },
     *     { id : 4, name : 'Penguin', kind : 'villain' }
     *   ]
     * });
     *
     * store.search('villain'); // [{ index : 2, data : joker-record }, { index : 3, data : penguin-record }]
     * ```
     *
     * @param {String} text Value to search for
     * @param {String[]} [fields] Fields to search value in, searches in all defined fields if not specified
     * @param {Function[]} [formatters] An array of field formatting functions to format the found value
     * @param {Boolean} [searchAllRecords] True to ignore any applied filters when searching
     * @returns {StoreSearchResult[]} Array of hits, in the format `{ index: x, data: record }`
     * @category Search
     */
    search(text, fields = null, formatters, searchAllRecords) {
      const records = this.isTree && !searchAllRecords ? this.rootNode.allChildren : this.getAllDataRecords(searchAllRecords), len = records.length, found = [];
      if (text == null) {
        return [];
      }
      if (typeof text === "string") {
        text = text.toLowerCase();
      }
      if (!fields) {
        fields = this.modelClass.allFields.map((field) => field.name);
      }
      let i, j, record, value2, valueType, comparison;
      for (i = 0; i < len; i++) {
        record = records[i];
        j = 0;
        for (const key of fields) {
          value2 = record.getValue(key);
          valueType = value2 instanceof Date ? "date" : typeof value2;
          const formatter = formatters == null ? void 0 : formatters[j];
          if (formatter) {
            value2 = formatter(value2);
            valueType = "string";
          }
          comparison = matchFns[valueType];
          if (value2 && (comparison == null ? void 0 : comparison(value2, text))) {
            found.push({
              index: i,
              data: record,
              field: key,
              id: record.id
            });
          }
          j++;
        }
      }
      return found;
    }
    /**
     * Find occurrences of the specified `value` in the specified `field` on all locally available records in the store
     * @param {String} field The record field to search in
     * @param {*} value Value to search for
     * @param {Boolean} distinct True to only return distinct matches, no duplicates
     * @param {Boolean} [searchAllRecords] True to ignore any applied filters when searching
     * @returns {StoreSearchResult[]} Array of hits, in the format { index: x, data: record }
     * @category Search
     */
    findByField(field, value2, distinct2 = false, searchAllRecords = false) {
      const records = this.getAllDataRecords(searchAllRecords), len = records.length, usedValues = /* @__PURE__ */ new Set(), found = [];
      let i, record, fieldValue;
      if (value2 != null) {
        value2 = String(value2).toLowerCase();
      }
      for (i = 0; i < len; i++) {
        record = records[i];
        fieldValue = record.getValue(field);
        if (!distinct2 || !usedValues.has(fieldValue)) {
          const type = fieldValue instanceof Date ? "date" : typeof fieldValue, matchFn = matchFns[type];
          if (value2 == null && fieldValue === value2 || value2 === "" && fieldValue === "" || value2 && matchFn(fieldValue, value2)) {
            found.push({
              id: record.id,
              index: i,
              data: record
            });
            if (distinct2) {
              usedValues.add(fieldValue);
            }
          }
        }
      }
      return found;
    }
    //endregion
    //region Find (single hit)
    /**
     * Finds the first record for which the specified function returns true
     * @param {Function} fn Comparison function, called with record as parameter
     * @param {Boolean} [searchAllRecords] True to ignore any applied filters when searching
     * @returns {Core.data.Model} Record or undefined if none found
     *
     * ```javascript
     * store.find(record => record.color === 'blue');
     * ```
     *
     * @category Search
     */
    find(fn2, searchAllRecords = false) {
      return this.getAllDataRecords(searchAllRecords).find(fn2);
    }
    /**
     * Finds the first record for which the specified field has the specified value
     * @param {String} fieldName Field name
     * @param {*} value Value to find
     * @param {Boolean} [searchAllRecords] True to ignore any applied filters when searching
     * @returns {Core.data.Model} Record or undefined if none found
     * @category Search
     */
    findRecord(fieldName, value2, searchAllRecords = false) {
      const matchFn = (r) => ObjectHelper.isEqual(r[fieldName], value2);
      if (this.isTree) {
        return this.query(matchFn, searchAllRecords)[0];
      }
      return this.getAllDataRecords(searchAllRecords).find(matchFn);
    }
    /**
     * Searches the Store records using the passed function.
     * @param {Function} fn A function that is called for each record. Return true to indicate a match
     * @param {Boolean} [searchAllRecords] True to ignore any applied filters when searching
     * @returns {Core.data.Model[]} An array of the matching Records
     * @category Search
     */
    query(fn2, searchAllRecords = false) {
      return this.getAllDataRecords(searchAllRecords).filter(fn2);
    }
    //endregion
    //region Others
    /**
     * Returns true if the supplied function returns true for any record in the store
     * @param {Function} fn A function that should return true to indicate a match
     * @param {Boolean} [searchAllRecords] True to ignore any applied filters when searching
     * @returns {Boolean}
     *
     * ```javascript
     * store.some(record => record.age > 95); // true if any record has age > 95
     * ```
     *
     * @category Search
     */
    some(fn2, searchAllRecords = false) {
      return this.getAllDataRecords(searchAllRecords).some(fn2);
    }
    //endregion
  }, __publicField(_a5, "$name", "StoreSearch"), _a5;
};

// ../Core/lib/Core/data/mixin/StoreSort.js
var StoreSort_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    //region Config
    static get defaultConfig() {
      return {
        /**
         * Use `localeCompare()` when sorting, which lets the browser sort in a locale specific order. Set to `true`,
         * a locale string or a locale config to enable.
         *
         * Enabling this has big negative impact on sorting
         * performance. For more info on `localeCompare()`, see [MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/localeCompare).
         *
         * Examples:
         *
         * ```javascript
         * const store = new Store({
         *     // Swedish sorting
         *     useLocaleSort : 'sv-SE'
         * });
         *
         * const store = new Store({
         *     // Swedish sorting with custom casing order
         *     useLocaleSort : {
         *         locale    : 'sv-SE',
         *         caseFirst : 'upper'
         *     }
         * });
         * ```
         *
         * Can also be configured on a per-sorter basis:
         *
         * ```javascript
         * store.sort({ field: 'name', useLocaleSort : 'sv-SE' });
         * ```
         *
         * @config {Boolean|String|Object}
         * @default false
         * @category Advanced
         */
        useLocaleSort: null
      };
    }
    //endregion
    //region Events
    /**
     * Fired before sorting
     * @event beforeSort
     * @param {Core.data.Store} source This Store
     * @param {Sorter[]} sorters Sorter configs
     * @param {Core.data.Model[]} records Records to sort
     */
    /**
     * Fired after sorting
     * @event sort
     * @param {Core.data.Store} source This Store
     * @param {Sorter[]} sorters Sorter configs
     * @param {Core.data.Model[]} records Sorted records
     */
    //endregion
    //region Properties
    /**
     * Currently applied sorters
     * @member {Sorter[]} sorters
     * @readonly
     * @category Sort, group & filter
     */
    /**
     * Is store sorted?
     * @property {Boolean}
     * @readonly
     * @category Sort, group & filter
     */
    get isSorted() {
      return Boolean(this.sorters.length) || this.isGrouped;
    }
    // Keeping for now in case it is useful elsewhere in the future
    // /**
    //  * Determines if the store is sorted as specified by its sorters. Only applicable for local sorting.
    //  * Note that this is an expensive operation that iterates all records.
    //  * @member {Boolean}
    //  * @internal
    //  */
    // get isInOrder() {
    //     if (!this.isSorted || this.remoteSort) {
    //         return false;
    //     }
    //
    //     const { sorterFn } = this;
    //
    //     let inOrder = true;
    //
    //     this.traverse(record => {
    //         const { previousSibling } = record;
    //         if (previousSibling) {
    //             if (sorterFn(previousSibling, record) > 0) {
    //                 inOrder = false;
    //                 return false;
    //             }
    //         }
    //     });
    //
    //     return inOrder;
    // }
    changeSorters(sorters) {
      return sorters.map((sorter) => this.normalizeSorterConfig(sorter, true));
    }
    updateSortParamName(param) {
      this.remoteSort = param != null;
    }
    //endregion
    //region Add & remove sorters
    /**
     * Sort records, either by replacing current sorters or by adding to them.
     * A sorter can specify a **_custom sorting function_** which will be called with arguments (recordA, recordB).
     * Works in the same way as a standard array sorter, except that returning `null` triggers the stores
     * normal sorting routine.
     *
     * ```javascript
     * // single sorter
     * store.sort('age');
     *
     * // single sorter as object, descending order
     * store.sort({ field : 'age', ascending : false });
     *
     * // multiple sorters
     * store.sort(['age', 'name']);
     *
     * // using custom sorting function
     * store.sort((recordA, recordB) => {
     *     // apply custom logic, for example:
     *     return recordA.name.length < recordB.name.length ? -1 : 1;
     * });
     *
     * // using locale specific sort (slow)
     * store.sort({ field : 'name', useLocaleSort : 'sv-SE' });
     * ```
     *
     * @param {String|Sorter[]|Sorter|Function} field Field to sort by.
     * Can also be an array of {@link Core.util.CollectionSorter sorter} config objects, or a sorting function, or a
     * {@link Core.util.CollectionSorter sorter} config.
     * @param {Boolean} [ascending] Sort order.
     * Applicable when the `field` is a string (if not specified and already sorted by the field, reverts direction),
     * or an object and `ascending` property is not specified for the object. `true` by default.
     * Not applicable when `field` is a function. `ascending` is always `true` in this case.
     * @param {Boolean} [add] If `true`, adds a sorter to the sorters collection.
     * Not applicable when `field` is an array. In this case always replaces active sorters.
     * @param {Boolean} [silent] Set as true to not fire events. UI will not be informed about the changes.
     * @category Sort, group & filter
     * @fires beforeSort
     * @fires sort
     * @fires Core.data.Store#event-refresh
     * @returns {Promise|null} If {@link Core/data/AjaxStore#config-sortParamName} is set on store, this method returns `Promise`
     * which is resolved after data is loaded from remote server, otherwise it returns `null`
     * @async
     */
    sort(field, ascending2, add = false, silent = false) {
      const me = this, records = me.allRecords, currentSorters = me.sorters ? me.sorters.slice() : [];
      let currentDir = null, curSort;
      if (field) {
        if (Array.isArray(field)) {
          me.sorters = field.map((sorter) => me.normalizeSorterConfig(sorter, typeof sorter === "string" ? true : ascending2));
        } else {
          const sorter = me.normalizeSorterConfig(field, ascending2);
          if (add) {
            curSort = me.getCurrentSorterByField(sorter.field);
            if (curSort) {
              currentDir = curSort.ascending;
              curSort.ascending = sorter.ascending;
            } else {
              me.sorters.push(sorter);
            }
          } else {
            me.sorters = [sorter];
          }
        }
      }
      if (!silent && me.trigger("beforeSort", { sorters: me.sorters, records, currentSorters }) === false) {
        me.sorters = currentSorters;
        if (currentDir !== null) {
          curSort.ascending = currentDir;
        }
        return null;
      }
      return me.remoteSort ? me.performRemoteSort(silent) : me.performSort(silent);
    }
    normalizeSorterConfig(field, ascending2) {
      var _a6, _b2, _c2;
      const me = this, sorter = { ascending: ascending2 };
      if (typeof field === "object") {
        ObjectHelper.assign(sorter, field);
        if (field.fn) {
          delete sorter.fn;
          sorter.sortFn = field.fn;
        }
        sorter.ascending = (_a6 = field.ascending) != null ? _a6 : ascending2;
      } else if (typeof field === "function") {
        sorter.sortFn = field;
      } else {
        sorter.field = field;
      }
      if (sorter.ascending == null) {
        const curSort = me.getCurrentSorterByField(sorter.field);
        sorter.ascending = curSort ? !curSort.ascending : true;
      }
      if (sorter.sortFn == null) {
        const compareItems = (_c2 = (_b2 = me.modelClass) == null ? void 0 : _b2.$meta.fields.map[sorter.field]) == null ? void 0 : _c2.compareItems;
        if (compareItems) {
          sorter.sortFn = compareItems;
        }
      }
      return sorter;
    }
    getCurrentSorterByField(field) {
      return typeof field === "string" && this.sorters.find((s) => s.field === field) || null;
    }
    /**
     * Add a sorting level (a sorter).
     * @param {String|Sorter[]|Sorter|Function} field Field to sort by. Can also be an array of sorters, or a sorting
     * function, or a {@link Core.util.CollectionSorter sorter} config.
     * @param {Boolean} [ascending] Sort order (used only if field specified as string)
     * @returns {Promise|null} If {@link Core/data/AjaxStore#config-sortParamName} is set on store, this method returns `Promise`
     * which is resolved after data is loaded from remote server, otherwise it returns `null`
     * @async
     * @category Sort, group & filter
     */
    addSorter(field, ascending2 = true) {
      return this.sort(field, ascending2, true);
    }
    /**
     * Remove a sorting level (a sorter)
     * @param {String|Function} field Stop sorting by this field (or sorter function)
     * @returns {Promise|null} If {@link Core/data/AjaxStore#config-sortParamName} is set on store, this method returns `Promise`
     * which is resolved after data is loaded from remote server, otherwise it returns `null`
     * @async
     * @category Sort, group & filter
     */
    removeSorter(field) {
      const sorterIndex = this.sorters.findIndex((sorter) => sorter.field === field || sorter.sortFn === field);
      if (sorterIndex > -1) {
        this.sorters.splice(sorterIndex, 1);
        return this.sort();
      }
    }
    /**
     * Removes all sorters, turning store sorting off.
     * @returns {Promise|null} If {@link Core/data/AjaxStore#config-sortParamName} is set on store, this method returns `Promise`
     * which is resolved after data is loaded from remote server, otherwise it returns `null`
     * @async
     * @category Sort, group & filter
     */
    clearSorters(silent = false) {
      if (this.sorters.length) {
        this.sorters.length = 0;
        return this.sort(void 0, void 0, void 0, silent);
      }
    }
    suspendSortOnUpdate() {
      if (!("_reapplySortersSuspended" in this)) {
        this._reapplySortersSuspended = 0;
      }
      this._reapplySortersSuspended++;
    }
    resumeSortOnUpdate(sort = true) {
      const me = this;
      if (me._reapplySortersSuspended) {
        me._reapplySortersSuspended--;
        if (!me._reapplySortersSuspended && me._shouldReapplySorters) {
          sort && me.sort();
          me._shouldReapplySorters = false;
        }
      }
    }
    //endregion
    //region Sorting logic
    /**
     * Creates a function used with Array#sort when sorting the store. Override to use your own custom sorting logic.
     * @param {Sorter[]} sorters An array of sorter config objects
     * @returns {Function}
     * @category Sort, group & filter
     */
    createSorterFn(sorters) {
      const storeLocaleSort = this.useLocaleSort;
      return (lhs, rhs) => {
        for (let i = 0; i < sorters.length; i++) {
          const sorter = sorters[i], { field, ascending: ascending2 = true, useLocaleSort = storeLocaleSort, caseSensitive = true } = sorter, fn2 = sorter.fn || sorter.sortFn, direction = ascending2 ? 1 : -1;
          if (fn2) {
            const val = fn2.call(sorter, lhs, rhs);
            if (val === 0) {
              continue;
            }
            if (val !== null) {
              return val * direction;
            }
          }
          let lhsValue = lhs.isModel ? lhs.getValue(field) : lhs[field], rhsValue = rhs.isModel ? rhs.getValue(field) : rhs[field];
          if (!caseSensitive) {
            if (typeof lhsValue === "string") {
              lhsValue = lhsValue.toLowerCase();
            }
            if (typeof rhsValue === "string") {
              rhsValue = rhsValue.toLowerCase();
            }
          }
          if (lhsValue === rhsValue) {
            continue;
          }
          if (lhsValue == null) {
            return -direction;
          }
          if (rhsValue == null) {
            return direction;
          }
          if (useLocaleSort && typeof lhsValue === "string") {
            if (useLocaleSort === true) {
              return String(lhsValue).localeCompare(rhsValue) * direction;
            }
            if (typeof useLocaleSort === "string") {
              return String(lhsValue).localeCompare(rhsValue, useLocaleSort) * direction;
            }
            if (typeof useLocaleSort === "object") {
              return String(lhsValue).localeCompare(rhsValue, useLocaleSort.locale, useLocaleSort) * direction;
            }
          }
          if (lhsValue > rhsValue) {
            return direction;
          }
          if (lhsValue < rhsValue) {
            return -direction;
          }
        }
        return 0;
      };
    }
    /**
     * The sorter function for sorting records in the store.
     * @member {Function}
     * @internal
     * @readonly
     */
    get sorterFn() {
      return this.createSorterFn(this.remoteSort ? [{ field: "_remoteSortIndex" }] : this.sorters);
    }
    /**
     * Perform sorting according to the {@link #config-sorters} configured.
     * This is the internal implementation which is overridden in {@link Core.data.AjaxStore} and
     * must not be overridden.
     * @async
     * @private
     * @category Sort, group & filter
     */
    performSort(silent) {
      const me = this, { rootNode, storage, sorterFn: sorter } = me;
      if (me.tree) {
        (!me.isChained || me.isChainedTree) && rootNode.traverse((node) => {
          if (node.isLoaded && node.isParent) {
            node.children.sort(sorter);
            node.updateChildrenIndices(node.children, "parentIndex", true);
          }
        });
        storage.replaceValues({
          values: me.collectDescendants(rootNode).visible,
          silent: true
        });
      } else if (me.isGrouped) {
        storage.replaceValues({
          ...me.prepareGroupRecords(),
          silent: true
        });
      } else {
        storage.replaceValues({
          values: storage.values.sort(sorter),
          silent: true
        });
      }
      me.afterPerformSort(silent || me.isRemoteDataLoading);
    }
    /**
     * Internal sort method, only called if {@link Core/data/Store#config-remoteSort} is set on store.
     * Should not be used in application code directly.
     * @param silent
     * @returns {Promise|null} If {@link Core/data/AjaxStore#config-sortParamName} is set on store, this method returns `Promise`
     * which is resolved after data is loaded from remote server, otherwise it returns `null`
     * @async
     * @internal
     */
    async performRemoteSort(silent) {
      var _a6;
      if (await ((_a6 = this.beforePerformSort) == null ? void 0 : _a6.call(this)) !== false && await this.performDataRequest(silent) !== false) {
        this.afterPerformSort(silent);
      }
    }
    buildRemoteParams(params = {}, encode = false) {
      var _a6;
      let {
        sorters,
        groupers,
        sortParamName = "sorters"
        // For AjaxStore
      } = this;
      (_a6 = super.buildRemoteParams) == null ? void 0 : _a6.call(this, params, encode);
      if (groupers) {
        sorters = groupers.concat(sorters);
      }
      if (sorters.length) {
        params[sortParamName] = encode ? this.encodeSorterParams(sorters) : this.getSorterParams(sorters);
      }
      return params;
    }
    getSorterParams(sorters) {
      return sorters.filter((sorter) => !sorter.sortFn).map(({ field, ascending: ascending2 }) => ({ field, ascending: ascending2 }));
    }
    afterPerformSort(silent) {
      if (silent) {
        return;
      }
      const me = this;
      me._idMap = null;
      const event = {
        action: "sort",
        sorters: me.sorters,
        records: me.allRecords
      };
      me.trigger("sort", event);
      me.trigger("refresh", event);
    }
    //endregion
  }, __publicField(_a5, "$name", "StoreSort"), __publicField(_a5, "configurable", {
    /**
     * Initial sorters, format is:
     * ```javascript
     * { sorters : [{ field: 'name', ascending: false }, ...] }
     * // or
     * { sorters : ['name', ...] }
     * ```
     * @config {Sorter[]|String[]}
     * @category Common
     */
    sorters: [],
    /**
     * Specify true to sort this store after records are added.
     * @config {Boolean}
     * @default
     * @category Sorting
     */
    reapplySortersOnAdd: false,
    /**
     * Specify `true` to reapply sorting when a record is updated in the store. You can also provide an array
     * of field names, to only trigger sort when certain data fields are updated.
     *
     * {@note}If the app processes many records or many record fields in a loop or similar, be sure to temporarily
     * disable this setting. Otherwise, each change will trigger a sort, which will make the processing slow{/@note}
     *
     * @prp {Boolean|Object} reapplySortersOnUpdate
     * @property {String[]} reapplySortersOnUpdate.fields Fields that reapply sorting when updated
     * @default
     * @category Filtering
     */
    reapplySortersOnUpdate: false,
    /**
     * Set this to `true` to activate remote sorting in this Store. This makes it possible to use the built-in
     * sorting features of the Store and corresponding UI functionality, without using local data.
     *
     * For a non-{@link Core.data.AjaxStore}, data will be requested by the Store by calling a by the app
     * implemented {@link Core.data.Store#config-requestData} function. Data can also be provided by listening to
     * the {@link Core.data.Store#event-requestData} event, and updating the {@link Core.data.Store#config-data}
     * property with new data.
     *
     * For {@link Core.data.AjaxStore}, data will be loaded via the configured
     * {@link Core.data.AjaxStore#config-readUrl}.
     *
     * @config {Boolean}
     * @category Sorting
     */
    remoteSort: false,
    /**
     * The name of the parameter to use to pass any sorters when loading data remotely.
     *
     * **Note:** When this is set, sorters must be defined using a field name and an ascending flag,
     * **not** a sort function.
     * @config {String}
     * @category Sorting
     */
    sortParamName: null
  }), _a5;
};

// ../Core/lib/Core/data/mixin/StoreChained.js
var returnTrue = () => true;
var StoreChained_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    //region Config
    static get defaultConfig() {
      return {
        /**
         * Function used to filter records in the masterStore into a chained store. If not provided,
         * all records from the masterStore will be included in the chained store.
         * Return `true` to include the passed record, or a `false` to exclude it.
         *
         * {@note}Only applies to chained stores{/@note}
         *
         * @config {Function}
         * @param {Core.data.Model} record
         * @returns {Boolean}
         * @category Chained store
         * @non-lazy-load
         */
        chainedFilterFn: null,
        /**
         * Array of field names that should trigger filtering of chained store when the fields are updated.
         *
         * {@note}Only applies to chained stores{/@note}
         *
         * @config {String[]}
         * @category Chained store
         * @non-lazy-load
         */
        chainedFields: null,
        /**
         * Master store that a chained store gets its records from.
         *
         * {@note}Only applies to chained stores{/@note}
         *
         * @config {Core.data.Store}
         * @category Chained store
         * @non-lazy-load
         */
        masterStore: null,
        /**
         * Method names calls to which should be relayed to master store.
         *
         * {@note}Only applies to chained stores{/@note}
         *
         * @config {String[]}
         * @category Chained store
         * @non-lazy-load
         */
        doRelayToMaster: ["add", "remove", "insert"],
        /**
         * Method names calls to which shouldn't be relayed to master store.
         *
         * {@note}Only applies to chained stores{/@note}
         *
         * @config {String}
         * @category Chained store
         * @non-lazy-load
         */
        dontRelayToMaster: [],
        /**
         * If `true`, collapsed records in original tree will be excluded from the chained store.
         *
         * {@note}Only applies to chained stores, and not when chaining using `chainTree()`{/@note}
         *
         * @config {Boolean}
         * @category Chained store
         * @non-lazy-load
         * @default
         */
        excludeCollapsedRecords: true,
        /**
         * If `true`, chained stores will be sorted when the master store is sorted. Note that this replaces
         * any existing sorters defined on the chained store.
         *
         * {@note}Only applies to chained stores{/@note}
         *
         * @config {Boolean}
         * @category Chained store
         * @default
         */
        syncSort: true,
        /**
         * If `true`, changing the order of records in a flat chained store (for example by using the `RowReorder`
         * feature in a Grid-based widget) will also change the order of records in the master store.
         *
         * {@note}Note that this config does not apply to chained tree stores (created using
         * {@link #function-chainTree}). Chained tree stores always sync the order of their records with the
         * master store.{/@note}
         *
         * Example usage:
         * ```javascript
         * store.chain(record => record.percent < 10, null, {
         *   syncOrder : true
         * });
         * ```
         *
         * {@note}The predictability of the outcome depends on the order of the records in both stores, and if the
         * subset of records in the chained store is contiguous. For example, if the master store has records
         * <code>A B C D E</code>, and the chained store has <code>E C A</code>, it will be difficult for users to
         * predict the outcome of moving <code>E</code> to between <code>C A</code>.{/@note}
         *
         * @config {Boolean}
         * @category Chained store
         * @default false
         */
        syncOrder: null,
        /**
         * Set to true to prevent including links (when grouping by array field)
         *
         * {@note}Only applies to chained stores{/@note}
         *
         * @config {Boolean}
         * @category Chained store
         * @non-lazy-load
         * @default false
         */
        ignoreLinkRecords: null,
        /**
         * If `true`, chained stores will apply filters from the master store. Filters flagged with `ignoreForChain`
         * will be ignored.
         *
         * {@note}Only applies to chained stores{/@note}
         *
         * @config {Boolean}
         * @category Chained store
         * @default false
         */
        chainFilters: null,
        chainSuspended: 0
      };
    }
    // All props should be predefined to work properly with objectified stores
    static get properties() {
      return {
        chainedStores: null,
        isChainedTree: null
      };
    }
    //endregion
    construct(config) {
      super.construct(config);
      const me = this, { masterStore } = me;
      if (masterStore) {
        me.methodNamesToRelay.forEach((fnName) => me[fnName] = (...params) => me.relayToMaster(fnName, params));
        me.removeAll = (...params) => {
          masterStore.remove(me.getRange(), ...params);
        };
        masterStore.ion({
          name: "masterStoreListeners",
          // HACK to have chained stores react early in an async events scenario (with engine). Could be turned
          // into a config, but this way one does not have to think about it
          changePreCommit: me.onMasterDataChangedPreCommit,
          change: me.onMasterDataChanged,
          prio: 1,
          thisObj: me
        });
        if (me.syncSort) {
          masterStore.ion({
            sort: () => me.sort(masterStore.sorters),
            thisObj: me
          });
        }
        if (!masterStore.chainedStores) {
          masterStore.chainedStores = [];
        }
        masterStore.chainedStores.push(me);
        me.fillFromMaster();
      }
    }
    //region Properties
    // For accessing the full set of records, whether chained or not
    get $master() {
      return this.masterStore || this;
    }
    /**
     * Is this a chained store?
     * @property {Boolean}
     * @readonly
     * @category Advanced
     * @non-lazy-load
     */
    get isChained() {
      return Boolean(this.masterStore);
    }
    set chainedFilterFn(chainedFilterFn) {
      this._chainedFilterFn = this.thisObj ? chainedFilterFn.bind(this.thisObj) : chainedFilterFn;
    }
    get chainedFilterFn() {
      return this._chainedFilterFn || returnTrue;
    }
    get methodNamesToRelay() {
      const doIsArray = Array.isArray(this.doRelayToMaster), dontIsArray = Array.isArray(this.dontRelayToMaster);
      return doIsArray && this.doRelayToMaster.filter((name) => !dontIsArray || !this.dontRelayToMaster.includes(name)) || [];
    }
    //endregion
    //region Internal
    updateChainedStores() {
      var _a6;
      (_a6 = this.chainedStores) == null ? void 0 : _a6.forEach((store) => store.fillFromMaster());
    }
    /**
     * Updates records available in a chained store by filtering the master store records using
     * {@link #config-chainedFilterFn}
     * @category Chained store
     * @non-lazy-load
     */
    fillFromMaster() {
      const me = this, { masterStore, isTree } = me, { isFiltered: masterFiltered } = masterStore;
      if (!me.isChained) {
        throw new Error("fillFromMaster only allowed on chained store");
      }
      if (me.isChainSuspended) {
        return;
      }
      let records, filterFn = null;
      if (me.chainFilters && masterFiltered) {
        const filters = masterStore.filters.values.filter((filter2) => !filter2.ignoreForChain);
        if (filters.length) {
          filterFn = CollectionFilter.generateFiltersFunction(filters);
        }
      }
      if (me.isChainedTree) {
        return me.fillTreeFromMaster(filterFn);
      }
      records = masterStore.allRecords.filter((r) => {
        if (!r.isSpecialRow && (!me.ignoreLinkRecords || !r.isLinked) && me.chainedFilterFn(r) && (!filterFn || filterFn(r))) {
          return !(me.excludeCollapsedRecords && !r.ancestorsExpanded(masterStore));
        }
      });
      if (me.masterStore.sorterFn && masterFiltered && masterStore.isSorted && !masterStore.remoteSort) {
        records.sort(masterStore.sorterFn);
      }
      if (isTree) {
        me.idRegister = {};
        me.internalIdRegister = {};
        records.forEach((r) => {
          if (r.stores.includes(me)) {
            me.register(r);
          } else {
            r.joinStore(me);
          }
        });
        if (me.excludeCollapsedRecords) {
          const children = me.getChildren(me.rootNode);
          records = me.doIncludeExclude(children, true);
        } else {
          records.forEach((r) => r.instanceMeta(me).collapsed = false);
        }
      }
      me.isFillingFromMaster = true;
      me.data = records;
      me.isFillingFromMaster = false;
    }
    // Should not be called directly, call fillFromMaster() instead
    fillTreeFromMaster(filterFn) {
      const me = this, { chainedFilterFn } = me, masterRoot = me.masterStore.rootNode, newParents = [], oldTreeLinkMap = me.treeLinkMap || {}, idRegister = me.idRegister = {}, internalIdRegister = me.internalIdRegister = {};
      me.treeLinkMap = {};
      WalkHelper.postWalk({ children: masterRoot.unfilteredChildren || masterRoot.children }, (r) => !r.isLeaf && (r.unfilteredChildren || r.children), (record) => {
        var _a6, _b2, _c2;
        if (
          // Include leaves that match filters
          record.isLeaf && (!filterFn || filterFn(record)) && chainedFilterFn(record) || // And parents that have a leaf included
          !record.isLeaf && ((_b2 = (_a6 = record.meta) == null ? void 0 : _a6.tempChildren) == null ? void 0 : _b2.length)
        ) {
          const parentMeta = (_c2 = record.parent) == null ? void 0 : _c2.meta, existingLink = oldTreeLinkMap[record.id], reuse = record.isLeaf && existingLink, link = reuse || record.link(), proxyData = link.proxyMeta.data;
          me.treeLinkMap[record.id] = link;
          if (reuse) {
            proxyData.parent = proxyData.parentIndex = proxyData.unfilteredIndex = proxyData.parentId = proxyData.children = proxyData.orderedChildren = proxyData.unfilteredChildren = null;
            idRegister[link.id] = existingLink;
            internalIdRegister[link.internalId] = existingLink;
          } else if (existingLink) {
            link.removeLink(existingLink);
          }
          if (!parentMeta.tempChildren) {
            parentMeta.tempChildren = [];
          }
          parentMeta.tempChildren.push(link);
          record.isParent && newParents.unshift(link);
        }
      });
      me.rootNode.meta.isFillingFromMaster = me.isFillingFromMaster = true;
      for (let i = newParents.length - 1; i >= 0; i--) {
        const record = newParents[i], { meta } = record.$original;
        if (meta.tempChildren) {
          record.meta.isFillingFromMaster = true;
          record.appendChild(meta.tempChildren, true);
          record.meta.isFillingFromMaster = false;
          meta.tempChildren = null;
        }
      }
      me.data = masterRoot.meta.tempChildren || [];
      me.rootNode.meta.isFillingFromMaster = me.isFillingFromMaster = false;
      masterRoot.meta.tempChildren = null;
    }
    /**
     * Commits changes back to master.
     * - the records deleted from chained store and present in master will be deleted from master
     * - the records added to chained store and missing in master will added to master
     * Internally calls {Store#function-commit commit()}.
     * @returns {Object} Changes, see Store#changes
     * @internal
     */
    commitToMaster() {
      const me = this, master = me.masterStore;
      if (!me.isChained) {
        throw new Error("commitToMaster only allowed on chained store");
      }
      master.beginBatch();
      master.remove(me.removed.values);
      master.add(me.added.values);
      master.endBatch();
      return me.commit();
    }
    /**
     * Relays some function calls to the master store
     * @private
     */
    relayToMaster(fnName, params) {
      return this.masterStore[fnName](...params);
    }
    // HACK, when used with engine the chained store will catch events early (sync) and prevent late (async) listeners
    onMasterDataChangedPreCommit(event) {
      this.onMasterDataChanged(event);
      this.$masterEventhandled = true;
    }
    /**
     * Handles changes in master stores data. Updates the chained store accordingly
     * @private
     */
    onMasterDataChanged({ action, changes, isMove }) {
      var _a6;
      const me = this;
      if (me.$masterEventhandled) {
        me.$masterEventhandled = false;
        return;
      }
      if (isMove && action === "remove") {
        return;
      }
      if (action !== "update" || me.chainedFields === "*" || ((_a6 = me.chainedFields) == null ? void 0 : _a6.some((field) => field in changes)) || "isLeaf" in changes || "parentId" in changes) {
        me.fillFromMaster();
      }
    }
    //endregion
    //region public API
    /**
     * Alias for {@link Core.data.Store#function-chain}
     *
     * {@note}If you want to chain a tree store, consider using {@link Core.data.Store#function-chainTree} instead. It
     * will create a new tree store with links to the records in this store. This will let you expand/collapse and
     * filter nodes in the chained store without affecting the original store.{/@note}
     *
     * @param {Function} [chainedFilterFn] An optional filter function called for every record to determine if it should
     * be included (return `true` / `false`). Leave it out to include all records.
     * @param {String[]} [chainedFields] Array of fields that trigger filtering when they are updated
     * @param {StoreConfig} [config] Additional chained store configuration. See {@link Core.data.Store#configs}
     * @returns {Core.data.Store}
     * @category Chained store
     * @non-lazy-load
     * @deprecated 6.1.2 Use chain() or chainTree() instead.
     */
    makeChained(chainedFilterFn = returnTrue, chainedFields, config) {
      VersionHelper.deprecate("core", "7.0.0", "makeChained() deprecated in favor of chain() and chainTree()");
      return this.chain(chainedFilterFn, chainedFields, config);
    }
    /**
     * Creates a chained store, a new Store instance that contains a subset of the records from current store.
     * Which records is determined by a filtering function, which is reapplied when data in the base store changes.
     *
     * ```javascript
     * // Chain all records
     * const all = store.chain();
     *
     * // Or a subset using a filter function
     * const oldies = store.chain(record => record.age > 50);
     * ```
     *
     * {@note}If you want to chain a tree store, consider using {@link Core.data.Store#function-chainTree} instead. It
     * will create a new tree store with links to the records in this store. This will let you expand/collapse and
     * filter nodes in the chained store without affecting the original store.{/@note}
     *
     * If this store is a {@link Core.data.mixin.StoreTree#property-isTree tree} store, then the resulting chained store
     * will be a tree store sharing the same root node, but only child nodes which pass the `chainedFilterFn` will be
     * considered when iterating the tree through the methods such as
     * {@link Core.data.Store#function-traverse} or {@link Core.data.Store#function-forEach}.
     *
     * @param {Function} [chainedFilterFn] An optional filter function called for every record to determine if it should
     * be included (return `true` / `false`). Leave it out to include all records.
     * @param {String[]} [chainedFields] Array of fields that trigger filtering when they are updated
     * @param {StoreConfig} [config] Additional chained store configuration. See {@link Core.data.Store#configs}
     * @returns {Core.data.Store}
     * @category Chained store
     * @non-lazy-load
     */
    chain(chainedFilterFn = returnTrue, chainedFields, config) {
      if (this.isChained) {
        const newChainedFilterFn = chainedFilterFn;
        chainedFilterFn = (r) => newChainedFilterFn(r) && this.chainedFilterFn(r);
      }
      return new ((config == null ? void 0 : config.storeClass) || this.constructor)({
        id: `${this.id}-chained-${StringHelper.generateUUID()}`,
        tree: false,
        autoTree: false,
        // Make sure we don't share instanceMeta with the master store
        ...config || {},
        // If someone ever chains a chained store, chain master instead
        masterStore: this.$master,
        modelClass: this.modelClass,
        // Chained store should never use syncDataOnLoad, that will create an infinite loop when they determine
        // that a record is added and then add it to master, repopulating this store and round we go
        syncDataOnLoad: false,
        chainedFilterFn,
        chainedFields
      });
    }
    /**
     * Creates a chained tree store, a new Store instance that contains a subset of the records from current store.
     * Which records is determined by a filtering function, which is reapplied when data in the base store changes.
     *
     * ```javascript
     * // Chain all nodes
     * const fullTree = store.chainTree();
     * // Or a subset
     * const oldies = store.chainTree(record => record.age > 50);
     * ```
     *
     * The resulting chained store will be a tree store with its own root node, under which all children are links to
     * the nodes in this store. This allows for expanding/collapsing and filtering nodes in the chained store without
     * affecting the original store.
     *
     * @param {Function} [chainedFilterFn] An optional filter function called for every leaf record to determine if it
     * should be included (return `true` / `false`). Leave it out to include all records.
     * @param {String[]} [chainedFields] Array of fields that trigger filtering when they are updated
     * @param {StoreConfig} [config] Additional chained store configuration. See {@link Core.data.Store#configs}
     * @returns {Core.data.Store}
     * @category Chained store
     * @non-lazy-load
     */
    chainTree(chainedFilterFn, chainedFields, config) {
      if (!this.tree) {
        throw new Error("chainTree only allowed on tree stores");
      }
      return this.chain(chainedFilterFn, chainedFields, {
        tree: true,
        isChainedTree: true,
        // Ensure we use same type of root node as the master store
        rootNode: this.rootNode.link(),
        ...config
      });
    }
    //endregion
    doDestroy() {
      var _a6;
      this.detachListeners("masterStoreListeners");
      (_a6 = this.chainedStores) == null ? void 0 : _a6.forEach((chainedStore) => chainedStore.destroy());
      super.doDestroy();
    }
    suspendChain() {
      this.chainSuspended++;
    }
    resumeChain(refill = false) {
      if (this.chainSuspended && !--this.chainSuspended && refill) {
        this.fillFromMaster();
      }
    }
    get isChainSuspended() {
      return this.chainSuspended > 0;
    }
  }, __publicField(_a5, "$name", "StoreChained"), _a5;
};

// ../Core/lib/Core/data/mixin/StoreState.js
var StoreState_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    /**
     * Get store state. Used by State-plugin to serialize state
     * @private
     * @returns {{ sorters, groupers }}
     */
    getState() {
      const { sorters, groupers, filters } = this, state = {};
      if (sorters == null ? void 0 : sorters.length) {
        state.sorters = sorters.map((sorter) => {
          const clone = ObjectHelper.cleanupProperties(ObjectHelper.clone(sorter));
          delete clone.fn;
          delete clone.sortFn;
          return clone;
        });
      }
      if (groupers == null ? void 0 : groupers.length) {
        state.groupers = groupers.map((grouper) => {
          const clone = ObjectHelper.cleanupProperties(ObjectHelper.clone(grouper));
          delete clone.fn;
          return clone;
        });
        state.collapsedGroups = [...this.collapsedGroups];
      }
      if (filters == null ? void 0 : filters.values.length) {
        state.filters = filters.allValues.map((filter2) => filter2.getState());
      }
      return state;
    }
    /**
     * Apply store state. Used by State-plugin to restore a previously serialized state
     * @private
     * @param {{ sorters, groupers }} state
     */
    applyState(state) {
      var _a6;
      const me = this, {
        sorters = [],
        groupers = [],
        filters = []
      } = state, {
        sortParamName,
        filterParamName
      } = me;
      if (me.isSorted || sorters.length !== 0 || groupers.length !== 0) {
        me.sorters = sorters.filter((sorter) => sorter.field || sorter.sortFn && !sortParamName);
        me.groupers = groupers.slice();
        if (groupers.length) {
          me.group();
        } else {
          me.sort();
        }
      }
      (_a6 = state.collapsedGroups) == null ? void 0 : _a6.forEach((id2) => {
        const record = me.getById(id2);
        if (record) {
          me.collapse(record, true);
        }
      });
      if (me.isFiltered || filters.length !== 0) {
        me.filters = filters.filter((filter2) => filter2.property || filter2.filterBy && !filterParamName);
        me.filter();
      }
    }
  }, __publicField(_a5, "$name", "StoreState"), _a5;
};

// ../Core/lib/Core/data/Wbs.js
var zeroPad = (v) => String(v).padStart(6, "0");
var Wbs = class _Wbs {
  /**
   * Wbs constructor.
   * @param {String|Number} value The value of WBS
   */
  constructor(value2) {
    this.value = value2;
    this._padded = null;
  }
  /**
   * The WBS value
   * @readonly
   * @member {String} value
   */
  set value(value2) {
    this._value = String(value2 != null ? value2 : "");
  }
  get value() {
    return this._value;
  }
  /**
   * Returns a `Wbs` instance given a `value`. If the `value` is already a `Wbs` object, it is returned. Otherwise,
   * a new `Wbs` is created. If `value` is `null` or `undefined`, that value is returned.
   * @param {String|Number|Core.data.Wbs} value
   * @returns {Core.data.Wbs}
   */
  static from(value2) {
    return value2 == null ? value2 : value2 instanceof _Wbs ? value2 : new _Wbs(value2);
  }
  /**
   * Returns a WBS code where each component is 0-padded on the left to 6 digits. That is "1.2" is padded to be
   * "000001.000002". These values can be compared for proper semantic order (e.g., Wbs.pad('1.2') < Wbs.pad('1.10')).
   * @param {String|Number|Core.data.Wbs} value
   * @returns {String}
   * @private
   */
  static pad(value2) {
    return value2 instanceof _Wbs ? value2.valueOf() : _Wbs.split(value2).map(zeroPad).join(".");
  }
  /**
   * Returns an array of digits from a given WBS code `value`. If the value cannot be converted, an empty array is
   * returned.
   * @param {String|Number|Core.data.Wbs} value
   * @returns {Number[]}
   * @private
   */
  static split(value2) {
    let i, ret;
    if (value2 || value2 === 0) {
      switch (typeof value2) {
        case "object":
          value2 = String(value2);
        case "string":
          ret = value2.split(".");
          for (i = ret.length; i-- > 0; ) {
            ret[i] = parseInt(ret[i], 10);
          }
          break;
        case "number":
          ret = [value2];
          break;
      }
    }
    return ret || [];
  }
  /**
   * Compares two WBS values, returning 0 if equal, -1 if `lhs` is less than `rhs, or 1 if `lhs` is greater than `rhs`.
   * @param {String|Core.data.Wbs} lhs
   * @param {String|Core.data.Wbs} rhs
   * @returns {Number}
   */
  static compare(lhs, rhs) {
    if (lhs === rhs) {
      return 0;
    }
    if (!lhs || !rhs) {
      return lhs ? 1 : rhs ? -1 : 0;
    }
    lhs = _Wbs.pad(lhs);
    rhs = _Wbs.pad(rhs);
    return lhs < rhs ? -1 : rhs < lhs ? 1 : 0;
  }
  /**
   * Appends a sub-level WBS value to this WBS code and returns a `Wbs` instance for it.
   * @param {String|Number} value
   * @returns {Core.data.Wbs}
   */
  append(value2) {
    const s = this.value;
    return _Wbs.from(s ? `${s}.${value2}` : value2);
  }
  /**
   * Returns truthy value if this Wbs equals the passed value.
   * @param {String|Core.data.Wbs} value
   * @returns {Boolean}
   */
  isEqual(value2) {
    return !_Wbs.compare(this, value2);
  }
  /**
   * Compares this WBS value with a specified pattern, returning `true` if they match. If the `pattern` is simply a
   * sequence of digits and decimal points (e.g., "1.2"), it is a match if it is a substring of this WBS code (e.g.,
   * "3.1.2.4"). If the `pattern` starts with `*` (e.g., "*.1.2"), it is a match if this WBS code ends with the text
   * following the `*` (e.g., "4.3.1.2"). If the `pattern` ends with `*`, it is a match if this WBS code starts with
   * the text up to the `*`.
   *
   * Some examples:
   * ```
   *  console.log(Wbs.from('1.2.3.4').match('2.3'));
   *  > true
   *  console.log(Wbs.from('1.2.3.4').match('*.4'));
   *  > true
   *  console.log(Wbs.from('1.2.3.4').match('1.2.*'));
   *  > true
   *
   *  console.log(Wbs.from('1.2.3.4').match('2.4'));
   *  > false
   *  console.log(Wbs.from('1.2.3.4').match('*.3'));
   *  > false
   *  console.log(Wbs.from('1.2.3.4').match('2.*'));
   *  > false
   * ```
   * @param {String} pattern A partial WBS code (e.g., "1.2"), optionally starting or ending with `*`.
   * @returns {Boolean}
   */
  match(pattern) {
    let ret = false;
    if (pattern) {
      const wbs = this.value, globLeft = pattern[0] === "*", globRight = pattern.endsWith("*"), n = pattern.length;
      if (globLeft === globRight) {
        ret = wbs.indexOf(globLeft ? pattern.substr(1, n - 2) : pattern) > -1;
      } else if (globLeft) {
        ret = wbs.endsWith(pattern.substr(1));
      } else {
        ret = wbs.startsWith(pattern.substr(0, n - 1));
      }
    }
    return ret;
  }
  toString() {
    return this.value;
  }
  toJSON() {
    return this.toString();
  }
  valueOf() {
    var _a5;
    return (_a5 = this._padded) != null ? _a5 : this._padded = _Wbs.pad(this.value);
  }
};
Wbs._$name = "Wbs";

// ../Core/lib/Core/data/mixin/StoreTree.js
var emptyArray4 = Object.freeze([]);
var StopBranch = Symbol("StopBranch");
var nullKeyUUID = StringHelper.generateUUID();
var StoreTree_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    get StopBranch() {
      return StopBranch;
    }
    //region Getters
    /**
     * `true` if this Store is configured to handle tree data (with `tree : true`) or if this is a
     * {@link Core.data.Store#function-chain chained store} and the master store is a tree store.
     * @property {Boolean}
     * @readonly
     * @category Tree
     */
    get isTree() {
      var _a6, _b2;
      return (_b2 = this.tree) != null ? _b2 : (_a6 = this.masterStore) == null ? void 0 : _a6.tree;
    }
    /**
     * Returns all leaf records in a tree store
     * @property {Core.data.Model[]}
     * @category Tree
     */
    get leaves() {
      const result = [];
      if (this.isTree) {
        this.traverse((record) => {
          if (record.isLeaf) {
            result.push(record);
          }
        });
      } else {
        this.allRecords.forEach((r) => {
          if (r.isLeaf) {
            result.push(r);
          }
          r.traverse((record) => {
            if (record.isLeaf) {
              result.push(record);
            }
          }, true);
        });
      }
      return result;
    }
    //endregion
    //region Children
    /**
     * Loads children for a parent node that uses load on demand (when expanding it). Base implementation does nothing,
     * either use AjaxStore which implements it, create your own subclass with an implementation or listen for
     * `toggleNode` and insert records when you have them available.
     * @param {Core.data.Model} parentRecord
     * @returns {Promise} A Promise which will be resolved if the load succeeds, and rejected if the load is
     * vetoed by a {@link Core.data.AjaxStore#event-beforeLoadChildren} handler, or if an {@link Core.data.AjaxStore#event-exception} is detected.
     * The resolved function is passed the event object passed to any event handlers.
     * The rejected function is passed the {@link Core.data.AjaxStore#event-exception} event if an exception occurred,
     * or `false` if the load was vetoed by a {@link Core.data.AjaxStore#event-beforeLoadChildren} handler.
     * @category Tree
     */
    async loadChildren(parentRecord) {
    }
    /**
     * Called from Model when adding children. Not to be called directly, use Model#appendChild() instead.
     * @internal
     * @param {Core.data.mixin.TreeNode} parent
     * @param {Core.data.mixin.TreeNode[]} children
     * @param {Number} index
     * @param {Object} [isMove]
     * @param {Boolean} [silent]
     * @fires add
     * @fires Core.data.Store#event-change
     * @category Tree
     */
    onNodeAddChild(parent, children, index, isMove, silent = false) {
      var _a6;
      const me = this, isRootLoad = parent === me.rootNode && parent.isLoading, { storage } = me, { previousSibling } = children[0];
      let storeInsertionPoint;
      const { visible: toAddToUI, all: toAdd } = me.collectDescendants(children, void 0, void 0, {
        inCollapsedBranch: !(parent.isExpanded(me) && parent.ancestorsExpanded(me)),
        applyFilter: me.isFiltered && me.reapplyFilterOnAdd
      });
      if (!isRootLoad && toAdd.length) {
        for (const record of toAdd) {
          if (!me.modified.includes(record) && !isMove[record.id]) {
            if (me.removed.includes(record)) {
              me.removed.remove(record);
            } else if (!record.isLinked) {
              me.added.add(record);
            }
          }
        }
      }
      if (isRootLoad && me.rootVisible) {
        toAddToUI.unshift(parent);
        toAdd.unshift(parent);
      }
      if (toAddToUI.length) {
        if (index === 0 || !previousSibling) {
          storeInsertionPoint = storage.indexOf(parent);
        } else {
          storeInsertionPoint = storage.indexOf(previousSibling) + previousSibling.getDescendantCount(true, me);
        }
        storage.suspendEvents();
        me.storage.splice(++storeInsertionPoint, 0, toAddToUI);
        storage.resumeEvents();
        me._idMap = null;
      } else {
        me._allRecords = null;
      }
      me.updateDependentStores("add", children);
      if (isRootLoad && toAddToUI.length) {
        if (me.sorters.length) {
          me.sort(null, null, false, true);
        }
        (_a6 = me.afterLoadData) == null ? void 0 : _a6.call(me);
        if (!silent) {
          const event = { action: "dataset", data: me._data, records: toAddToUI };
          me.trigger("refresh", event);
          me.trigger("change", event);
        }
      } else if (!silent) {
        const event = { action: "add", parent, isChild: true, isMove, records: children, allRecords: toAdd, index: storeInsertionPoint };
        me.trigger("add", event);
        me.trigger("change", event);
        if (isMove && Object.values(isMove).some((wasMoved) => wasMoved)) {
          const event2 = {
            newParent: parent,
            records: children.filter((record) => isMove[record.id]),
            oldParents: children.map((child) => {
              return me.getById(child.meta.oldParentId);
            })
          };
          me.trigger("move", event2);
        }
      }
    }
    onNodeRemoveChild(parent, children, index, flags = { isMove: false, silent: false, unfiltered: false }) {
      const me = this, { storage } = me, toRemoveFromUI = [], toRemove = [], { isMove, silent, unfiltered } = flags, removeUnfiltered = unfiltered && me.isFiltered;
      me.collectDescendants(
        children,
        toRemoveFromUI,
        toRemove,
        { inCollapsedBranch: !(parent.isExpanded(me) && parent.ancestorsExpanded(me)), unfiltered: removeUnfiltered }
      );
      if (!isMove) {
        for (const record of children) {
          record.unjoinStore(me);
        }
        for (const record of toRemove) {
          if (record.stores.includes(me)) {
            record.unjoinStore(me);
          }
          if (me.added.includes(record)) {
            me.added.remove(record);
          } else if (!record.isLinked) {
            me.removed.add(record);
          }
        }
        me.modified.remove(toRemove);
      }
      if (toRemoveFromUI.length) {
        index = storage.indexOf(toRemoveFromUI[0]);
        if (index > -1) {
          storage.suspendEvents();
          storage.splice(index, toRemoveFromUI.length);
          storage.resumeEvents();
          me._idMap = null;
        }
      } else {
        index = -1;
        me._allRecords = null;
      }
      if (!silent && (me.fireRemoveEventForMoveAction || !isMove)) {
        const event = {
          action: "remove",
          parent,
          isChild: true,
          isMove,
          records: children,
          allRecords: toRemove,
          index
        };
        me.trigger("remove", event);
        me.trigger("change", event);
      }
      return toRemove;
    }
    // IMPORTANT when using `applyFilter` option, should use the return value of this function
    // instead of relying on arguments mutation
    collectDescendants(node, visible = [], all = [], flags = {}) {
      var _a6;
      const me = this, { inCollapsedBranch = false, unfiltered = false, applyFilter = false } = flags, children = Array.isArray(node) ? node : (_a6 = me.getChildren(node, unfiltered)) != null ? _a6 : [];
      if (applyFilter) {
        return {
          visible: !inCollapsedBranch && children.flatMap((child) => me.collectVisibleNodeDescendantsFiltered(child)),
          all: children.concat(children.flatMap((child) => child.allChildren))
        };
      } else {
        for (let i = 0, len = children.length, child; i < len; i++) {
          child = children[i];
          if (!inCollapsedBranch) {
            visible.push(child);
          }
          all.push(child);
          me.collectDescendants(child, visible, all, {
            inCollapsedBranch: inCollapsedBranch || !child.isExpanded(me),
            unfiltered
          });
        }
        return { visible, all };
      }
    }
    collectVisibleNodeDescendantsFiltered(node) {
      const children = node.unfilteredChildren || node.children;
      if (!children || children.length === 0 || !node.isLeaf && !node.isExpanded(this)) {
        return this.filtersFunction(node) ? [node] : [];
      }
      const filteredChildren = children.flatMap((child) => this.collectVisibleNodeDescendantsFiltered(child));
      return filteredChildren.length || this.filtersFunction(node) ? [node].concat(filteredChildren) : [];
    }
    /**
     * Returns the children of the passed branch node which this store owns. By default, this
     * is the entire `children` array.
     *
     * **If this store {@link Core.data.mixin.StoreChained#property-isChained}**, then
     * this returns only the subset of children which are filtered into this store by the
     * {@link Core.data.mixin.StoreChained#config-chainedFilterFn chainedFilterFn}.
     * @param {Core.data.Model} parent The node to return the children of.
     * @returns {Core.data.Model[]}
     * @category Tree
     */
    getChildren(parent, unfiltered = false) {
      const me = this, chained = me.isChained && !me.isChainedTree, children = (unfiltered || chained) && parent.unfilteredChildren || parent.children;
      return !(children == null ? void 0 : children.length) ? emptyArray4 : chained ? children.filter(me.chainedFilterFn).sort(me.sorterFn) : children;
    }
    /**
     * Includes or excludes all records beneath parentRecord in storage. Used when expanding or collapsing
     * nodes.
     * @private
     * @param parent Parent record
     * @param include Include (true) or exclude (false)
     * @category Tree
     */
    internalToggleTreeSubRecords(parent, include) {
      const me = this, { storage } = me, index = storage.indexOf(parent), children = me.doIncludeExclude(me.getChildren(parent), include);
      if (me.isFiltered && include && parent.unfilteredChildren) {
        me.updateChildrenHiddenState(parent);
      }
      if (children.length && index !== false) {
        storage.suspendEvents();
        if (include) {
          storage.splice(index + 1, 0, children);
          const event = { action: "add", isExpand: true, parent, records: children, index: index + 1 };
          me.trigger("add", event);
          me.trigger("change", event);
        } else {
          storage.splice(index + 1, children.length);
          const event = { action: "remove", isCollapse: true, parent, records: children, index: index + 1 };
          me.trigger("remove", event);
          me.trigger("change", event);
        }
        storage.resumeEvents();
        me._idMap = null;
      }
    }
    // Updates the hidden flag of its children while store is filtered
    updateChildrenHiddenState(parentRecord) {
      var _a6;
      (_a6 = parentRecord.unfilteredChildren) == null ? void 0 : _a6.forEach((child) => {
        child.instanceMeta(this.id).hidden = false;
        if (!child.isLeaf) {
          this.updateChildrenHiddenState(child);
        }
      });
    }
    doIncludeExclude(children, include, result = []) {
      const me = this, childCount = (children == null ? void 0 : children.length) || 0;
      for (let i = 0; i < childCount; i++) {
        const child = children[i];
        if (!me.isChained || me.chainedFilterFn(child)) {
          const mapMeta = child.instanceMeta(me.id);
          if (include || !mapMeta.hidden) {
            result.push(child);
          }
          mapMeta.hidden = !include;
          if (child.isExpanded(me)) {
            me.doIncludeExclude(me.getChildren(child), include, result);
          }
        }
      }
      return result;
    }
    /**
     * Collapse an expanded record or expand a collapsed. Optionally forcing a certain state.
     * @param {String|Number|Core.data.Model} idOrRecord Record (the record itself) or id of a record to toggle
     * @param {Boolean} [collapse] Force collapse (true) or expand (false)
     * @category Tree
     */
    async toggleCollapse(idOrRecord, collapse) {
      const me = this, record = me.getById(idOrRecord), meta = record.instanceMeta(me);
      if (collapse === void 0) {
        collapse = !meta.collapsed;
      }
      if (!meta.isLoadingChildren && !record.isLeaf && record.isExpanded(me) === collapse) {
        me.trigger("beforeToggleNode", { record, collapse });
        meta.collapsed = collapse;
        if (meta.collapsed) {
          me.onNodeCollapse(record);
          return true;
        } else {
          me.onNodeExpand(record);
          let success2 = true;
          if (!record.isLoaded) {
            meta.isLoadingChildren = true;
            try {
              await me.loadChildren(record);
            } catch (exception) {
              meta.collapsed = true;
              success2 = false;
              me.trigger("loadChildrenException", { record, exception });
            } finally {
              meta.isLoadingChildren = false;
            }
          }
          return success2;
        }
      }
    }
    /**
     * Remove all records beneath parentRecord from storage.
     * @private
     * @param parentRecord Parent record
     * @category Tree
     */
    onNodeCollapse(parentRecord) {
      if (parentRecord.ancestorsExpanded(this)) {
        return this.internalToggleTreeSubRecords(parentRecord, false);
      }
    }
    /**
     * Add all records beneath parentRecord from storage.
     * @private
     * @param parentRecord Parent record
     * @category Tree
     */
    onNodeExpand(parentRecord) {
      if (parentRecord.ancestorsExpanded(this)) {
        return this.internalToggleTreeSubRecords(parentRecord, true);
      }
    }
    //endregion
    //region Transform flat data
    /**
     * Transforms flat data containing parent ids into tree data
     * @param {Object[]} data Flat raw data
     * @returns {Object[]} Tree data
     * @private
     */
    transformToTree(data) {
      var _a6;
      const {
        parentIdField,
        idField,
        childrenField
      } = this.modelClass, parentIdDataProp = ((_a6 = this.modelClass.fieldMap[parentIdField]) == null ? void 0 : _a6.dataSource) || "parentId", indexById = /* @__PURE__ */ new Map(), parentIds = /* @__PURE__ */ new Set(), transformed = [];
      for (const node of data) {
        const id2 = node == null ? void 0 : node[idField];
        if (id2 != null) {
          indexById.set(id2, node);
        }
      }
      const cloneParent = (node) => {
        const clone = Object.assign({}, node);
        clone[childrenField] = [];
        indexById.set(clone[idField], clone);
        parentIds.add(node[idField]);
        return clone;
      };
      for (let node of data) {
        if (node) {
          const nodeId = node[idField];
          if (parentIds.has(nodeId)) {
            node = indexById.get(nodeId);
          } else {
            node = cloneParent(node);
          }
          const parentId = node[parentIdDataProp];
          if (parentId != null) {
            let parent = indexById.get(parentId);
            if (parent) {
              if (!parent[childrenField]) {
                parent = cloneParent(parent);
              }
              parent[childrenField].push(node);
            }
          } else {
            if (node[childrenField]) {
              transformed.push(node);
            } else if (node[idField] != null) {
              transformed.push(cloneParent(node));
            } else {
              transformed.push(node);
            }
          }
        }
      }
      indexById.forEach((parent) => {
        var _a7, _b2;
        (_b2 = (_a7 = parent[childrenField]).sort) == null ? void 0 : _b2.call(_a7, (child1, child2) => child1.parentIndex - child2.parentIndex);
      });
      return transformed;
    }
    /**
     * Transforms passed data into a tree with parent levels based on supplied fields.
     *
     * ```javascript
     * const newRoot = store.treeify(
     *     [
     *         { name : "Julia", age : 21 },
     *         { name : "Macie", age : 12 },
     *         { name : "Julia", age : 18 },
     *         { name : "Olivia", age : 8 },
     *     ],
     *     ['name', r => r.age % 10]
     * );
     * ```
     *
     * Generated parent records are indicated with `generatedParent` and `key` properties. The first one is set to
     * `true` and the latter one has a value for the group the parent represents.
     *
     * @param {Array<String|Function>} fields The field names, or a function to call to extract a value to create parent
     * nodes for records with the same value.
     * @param {Function} [parentTransform] A function which is called to allow the caller to transform the raw data
     * object of any newly created parent nodes.
     * @param {Boolean} [convertParents] Pass `true` to convert raw new parent data objects to this Store's
     * {@link Core.data.Store#config-modelClass}.
     * @privateparam {Function} [valueFormatter] A function which is called to allow the caller to format the raw data
     * before grouping
     * @returns {Core.data.Model} New root node
     * @internal
     */
    treeifyData(data, fields, parentTransform, convertParents = false, valueFormatter) {
      const { length } = fields, parents = [], orphans = [], newRoot = {};
      let i, lastParents;
      for (i = 0; i < length; i++) {
        let field = fields[i];
        field = field.field || field;
        if (!fields[i].call) {
          fields[i] = (n) => n[field];
          fields[i].fieldName = field;
        }
        parents[i] = /* @__PURE__ */ new Map();
      }
      WalkHelper.preWalkWithParent({ isRoot: true, id: this.rootNode.id, children: data }, (n) => n.children, (parent, n) => {
        lastParents = [];
        if (n.isLeaf) {
          for (i = 0; i < length; i++) {
            const field = fields[i], fieldValue = valueFormatter ? valueFormatter(field(n), field.fieldName) : field(n), nodeMap = parents[i], key = fieldValue === "" ? nullKeyUUID : fieldValue, keys = ArrayHelper.asArray(key) || [nullKeyUUID], newParents = [];
            if (keys[0] === StopBranch) {
              break;
            }
            const lastParentsToIterate = lastParents.length > 0 ? lastParents : [null];
            lastParentsToIterate.forEach((lastParent) => {
              var _a6;
              for (let j = 0, { length: length2 } = keys; j < length2; j++) {
                const key2 = keys[j], path = `${((_a6 = lastParent == null ? void 0 : lastParent.path) != null ? _a6 : "") + ((key2 == null ? void 0 : key2.isModel) ? key2.id : key2)}/`, id2 = StringHelper.makeValidDomId(`generated_${path}`, "_"), parent2 = nodeMap.get(id2) || nodeMap.set(id2, {
                  id: id2,
                  key: keys[j] === nullKeyUUID ? "" : key2,
                  path,
                  readOnly: true,
                  children: [],
                  generatedParent: true,
                  field: field.fieldName,
                  firstGroupChild: n
                }).get(id2);
                newParents.push(parent2);
                if (lastParent && !lastParent.children.includes(parent2)) {
                  lastParent.children.push(parent2);
                }
              }
            });
            lastParents = newParents;
          }
          if (lastParents.length) {
            lastParents.forEach((lastParent, j) => lastParent.children.push(j ? n.link() : n));
          } else {
            orphans.push(n);
          }
        }
      }, true);
      if (parentTransform || convertParents) {
        parents.forEach((p) => p.forEach((p2, id2, map2) => {
          parentTransform == null ? void 0 : parentTransform(p2);
          if (convertParents) {
            p2 = this.createRecord(p2);
            map2.set(id2, p2);
          }
        }));
      }
      newRoot.children = [...parents[0].values(), ...orphans];
      const sort = (n) => {
        if (n.children) {
          n.children.sort((lhs, rhs) => {
            if (lhs.isLeaf === rhs.isLeaf) {
              if (lhs.isLeaf) {
                let result;
                for (let i2 = 0; !result && i2 < length; i2++) {
                  const lv = fields[i2](lhs), rv = fields[i2](rhs);
                  if (lv === StopBranch || rv === StopBranch) {
                    return lhs.isLeaf < rhs.isLeaf ? -1 : lhs.isLeaf > rhs.isLeaf ? 1 : 0;
                  }
                  result = lv < rv ? -1 : rv < lv ? 1 : 0;
                }
                return result;
              } else if (typeof lhs.key === "string" && typeof rhs.key === "string") {
                return lhs.key.localeCompare(rhs.key, void 0, { numeric: true });
              } else {
                return lhs.key < rhs.key ? -1 : lhs.key > rhs.key ? 1 : 0;
              }
            } else {
              return lhs.isLeaf < rhs.isLeaf ? -1 : lhs.isLeaf > rhs.isLeaf ? 1 : 0;
            }
          });
          n.children.forEach(sort);
        }
      };
      sort(newRoot);
      return newRoot;
    }
    /**
     * Transforms this store data into a tree with parent levels based on supplied fields.
     *
     * ```javascript
     * const newRoot = store.treeify(['name', r => r.age % 10]);
     * ```
     *
     * Generated parent records are indicated with `generatedParent` and `key` properties. The first one is set to
     * `true` and the latter one has a value for the group the parent represents.
     *
     * @param {Array<String|Function>} fields The field names, or a function to call to extract a value to create parent
     * nodes for records with the same value.
     * @param {Function} [parentTransform] A function which is called to allow the caller to transform the raw data
     * object of any newly created parent nodes.
     * @param {Boolean} [convertParents] Pass `true` to convert raw new parent data objects to this Store's
     * {@link Core.data.Store#config-modelClass}.
     * @privateparam {Function} [valueFormatter] A function which is called to allow the caller to format the raw data
     * before grouping
     * @returns {Core.data.Model} New root node
     * @internal
     */
    treeify(fields, parentTransform, convertParents = false, valueFormatter) {
      return this.treeifyData(this.rootNode.children, fields, parentTransform, convertParents = false, valueFormatter);
    }
    //endregion
    treeifyFlatData(data) {
      var _a6;
      const { childrenField, parentIdField } = this.modelClass, parentIdDataProp = ((_a6 = this.modelClass.fieldMap[parentIdField]) == null ? void 0 : _a6.dataSource) || "parentId";
      let hasParentId = false, shouldTransform = true;
      for (const node of data) {
        if ((node == null ? void 0 : node.isModel) || Array.isArray(node == null ? void 0 : node[childrenField])) {
          shouldTransform = false;
          break;
        }
        if ((node == null ? void 0 : node[parentIdDataProp]) != null) {
          hasParentId = true;
        }
      }
      if (shouldTransform && hasParentId) {
        data = this.transformToTree(data);
      }
      return data;
    }
    /**
     * Increase the indentation level of one or more nodes in the tree
     * @param {Core.data.Model|Core.data.Model[]} nodes The nodes to indent.
     * @fires indent
     * @fires Core.data.Store#event-change
     * @category Tree
     * @non-lazy-load
     */
    async indent(nodes) {
      const me = this;
      nodes = Array.isArray(nodes) ? nodes : [nodes];
      nodes = nodes.filter((node) => {
        let result = Boolean(node.previousSibling);
        while (result && !node.isRoot) {
          result = !nodes.includes(node.parent);
          node = node.parent;
        }
        return result;
      });
      if (nodes.length && me.trigger("beforeIndent", { records: nodes }) !== false) {
        nodes.sort((lhs, rhs) => Wbs.compare(lhs.wbsCode, rhs.wbsCode));
        me.beginBatch();
        for (const node of nodes) {
          const newParent = node.previousSibling;
          newParent.appendChild(node);
          me.toggleCollapse(newParent, false);
        }
        me.endBatch();
        me.trigger("indent", { records: nodes });
        me.trigger("change", {
          action: "indent",
          records: nodes
        });
      }
    }
    /**
     * Decrease the indentation level of one or more nodes in the tree
     * @param {Core.data.Model|Core.data.Model[]} nodes The nodes to outdent.
     * @fires outdent
     * @fires Core.data.Store#event-change
     * @category Tree
     * @non-lazy-load
     */
    async outdent(nodes) {
      const me = this;
      nodes = Array.isArray(nodes) ? nodes : [nodes];
      nodes = nodes.filter((node) => {
        const { parent } = node;
        let result = parent && !parent.isRoot;
        while (result && !node.isRoot) {
          result = !nodes.includes(parent);
          node = node.parent;
        }
        return result;
      });
      if (nodes.length && me.trigger("beforeOutdent", { records: nodes }) !== false) {
        nodes.sort((lhs, rhs) => Wbs.compare(lhs.wbsCode, rhs.wbsCode));
        me.beginBatch();
        for (const node of nodes) {
          const { parent } = node, newChildren = parent.children.slice(parent.children.indexOf(node) + 1);
          parent.parent.insertChild(node, parent.nextSibling);
          node.appendChild(newChildren);
          me.toggleCollapse(node, false);
        }
        me.endBatch();
        me.trigger("outdent", { records: nodes });
        me.trigger("change", {
          action: "outdent",
          records: nodes
        });
      }
    }
  }, __publicField(_a5, "$name", "StoreTree"), /**
   * A special `Symbol` signalizing treeify method that the current record grouping should be stopped.
   *
   * ```javascript
   * const newRoot = workerStore.treeify([
   *     // group workers by company
   *     worker => {
   *         // if the worker is unemployed we don't put it in a group
   *         // we just show such record on the root level
   *         if (!worker.company) {
   *             return Store.StopBranch
   *         }
   *
   *         return worker.company;
   *     }
   * ]);
   * ```
   * @member {Symbol} StopBranch
   * @static
   * @category Advanced
   */
  __publicField(_a5, "StopBranch", StopBranch), __publicField(_a5, "configurable", {
    /**
     * Set to `true` to on load transform a flat dataset with raw objects containing `parentId` into the format
     * expected for tree data.
     *
     * Example input format:
     *
     * ```javascript
     * [
     *   { id : 1, name : 'Parent' },
     *   { id : 2, name : 'Child', parentId : 1 }
     * ]
     * ```
     *
     * Will be transformed into:
     *
     * ```javascript
     * [
     *   {
     *     id       : 1,
     *     name     : 'Parent',
     *     children : [
     *       { id : 2, name : 'Child', parentId : 1 }
     *     ]
     *   }
     * ]
     * ```
     *
     * @config {Boolean}
     * @category Tree
     */
    transformFlatData: null,
    /**
     * Set to `true` to fire a 'remove' event when moving a node (to mimic the behavior of versions < 6.0).
     * @config {Boolean}
     * @category Tree
     * @default
     */
    fireRemoveEventForMoveAction: false,
    /**
     * When set to `true`, restores the expanded states of tree nodes when reloading data.
     * @config {Boolean}
     * @default false
     */
    keepExpandedOnLoad: false
  }), _a5;
};

// ../Core/lib/Core/data/mixin/StoreSync.js
var StoreSync_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    static get configurable() {
      return {
        /**
         * Configure with `true` to sync loaded data instead of replacing existing with a new dataset.
         *
         * By default (or when configured with `false`) assigning to `store.data` replaces the entire dataset
         * with a new one, creating all new records:
         *
         * ```javascript
         * store.data = [ { id : 1, name : 'Saitama' } ];
         *
         * const first = store.first;
         *
         * store.data = [ { id : 1, name : 'One-Punch man' } ];
         *
         * // store.first !== first;
         * ```
         *
         * When configured with `true` the new dataset is instead synced against the old, figuring out what was
         * added, removed and updated:
         *
         * ```javascript
         * store.data = [ { id : 1, name : 'Saitama' } ];
         *
         * const first = store.first;
         *
         * store.data = [ { id : 1, name : 'One-Punch man' } ];
         *
         * // store.first === first;
         * ```
         *
         * After the sync, any configured sorters, groupers and filters will be reapplied.
         *
         * #### Threshold
         *
         * The sync operation has a configurable threshold, above which the operation will be treated as a
         * batch/refresh and only trigger a single `refresh` event. If threshold is not reached, individual events
         * will be triggered (single `add`, `remove` and possible multiple `update`). To enable the threshold,
         * supply a config object with a `threshold` property instead of `true`:
         *
         * ```javascript
         * const store = new Store({
         *     syncDataOnLoad : {
         *         threshold : '20%'
         *     }
         * });
         * ```
         *
         * `threshold` accepts numbers or strings. A numeric threshold means number of affected records, while a
         * string is used as a percentage of the whole dataset (appending `%` is optional). By default no threshold
         * is used.
         *
         * #### Missing fields
         *
         * The value of any field not supplied in the new dataset is by default kept as is (if record is not removed
         * by the sync). This behaviour is configurable, by setting `keepMissingValues : false` in a config object
         * it will reset any unspecified field back to their default values:
         *
         * ```javascript
         * const store = new Store({
         *     syncDataOnLoad : {
         *         keepMissingValues : false
         *     }
         * });
         * ```
         *
         * Considering the following sync operation:
         *
         * ```javascript
         * // Existing data
         * { id : 1, name : 'Saitama', powerLevel : 100 }
         * // Sync data
         * { id : 1, name : 'One-Punch Man' }
         * ```
         *
         * The result would by default (or when explicitly configured with `true`)  be:
         *
         * ```javascript
         * { id : 1, name : 'One-Punch Man', powerLevel : 100 }
         * ```
         *
         * If configured with `keepMissingValues : false` it would instead be:
         *
         * ```javascript
         * { id : 1, name : 'One-Punch Man' }
         * ```
         *
         * <div class="note">Never enable `syncDataOnLoad` on a chained store, it will create an infinite loop when
         * it is populated from the main store (the main store can use the setting)</div>
         *
         * @config {Boolean|SyncDataOnLoadOptions} syncDataOnLoad
         * @default false
         * @category Common
         * @non-lazy-load
         */
        syncDataOnLoad: null,
        /**
         * Experimental hook to allow app to determine if sync should be performed, and/or for which records.
         *
         * @config {Function} shouldSyncDataset
         * @param {Object} options
         * @param {Object[]} options.data New dataset, an array of incoming data objects
         * @param {Core.data.Store} options.store This store
         * @param {Core.data.Model[]} options.records Records currently in this store
         * @returns {Set|Boolean} A set of ids to apply changes to, or `false` to skip sync
         * @private
         */
        shouldSyncDataset: null,
        shouldSyncRecord: null,
        /**
         * When using {@link #config-syncDataOnLoad} and the store is neither sorted nor grouped, the order of the
         * incoming dataset is respected. This has a cost, since records in the store might have to be moved around.
         *
         * For stores where the order is not important, this behavior can be disabled to improve performance by
         * configuring `reorderOnSync` as `false`.
         *
         * @default
         * @private
         */
        reorderOnSync: true
      };
    }
    /**
     * Syncs a new dataset against the already loaded one, only applying changes.
     * Not intended to be called directly, please configure store with `syncDataOnLoad: true` and assign to
     * `store.data` as usual instead.
     *
     * ```
     * const store = new Store({
     *    syncDataOnLoad : true,
     *    data : [
     *        // initial data
     *    ]
     * });
     *
     * store.data = [ // new data ]; //  Difference between initial data and new data will be applied
     * ```
     *
     * @param {Object[]|Core.data.Model[]} data New dataset, an array of records or data objects
     * @private
     */
    syncDataset(data) {
      var _a6, _b2, _c2, _d, _e;
      const me = this, { storage } = me, idsToCheck = (_d = (_a6 = me.shouldSyncDataset) == null ? void 0 : _a6.call(me, { data, store: me, records: me.records })) != null ? _d : (
        // If part of project, it might have an aggregated version of the hook
        (_c2 = (_b2 = me.project) == null ? void 0 : _b2.shouldSyncDataset) == null ? void 0 : _c2.call(_b2, { data, store: me, records: me.records })
      );
      if (idsToCheck === false) {
        return;
      }
      me.trigger("startApplyChangeset");
      me.isSyncingDataOnLoad = true;
      const { toAdd, toRemove, toMove, updated, ids } = me.tree ? me.syncTreeDataset(data, idsToCheck) : me.syncFlatDataset(data, idsToCheck);
      let threshold = (_e = me.syncDataOnLoad) == null ? void 0 : _e.threshold, surpassed = false;
      if (threshold) {
        if (typeof threshold === "string") {
          threshold = parseInt(threshold, 10) / 100 * me.count;
        }
        surpassed = toAdd.length + toRemove.length + toMove.length + updated.length > threshold;
      }
      if (me.tree) {
        if (toAdd.length) {
          const added = me.add(toAdd, surpassed);
          added.forEach((node) => node.clearParentId());
        }
        if (toMove.length) {
          for (const { parent, node, index } of toMove) {
            const newParent = me.getById(parent.id);
            newParent.insertChild(node, index);
          }
        }
        me.remove(toRemove, surpassed);
      } else {
        if (surpassed) {
          me.suspendEvents();
        }
        me.remove(toRemove);
        me.add(toAdd);
        if (surpassed) {
          me.resumeEvents();
        }
      }
      if (threshold && !surpassed) {
        updated.forEach(({ record, toSet, wasSet }) => me.onModelChange(record, toSet, wasSet));
      }
      if (!me.$isSettingStoreFieldData) {
        me.acceptChanges();
      }
      const event = { added: toAdd, removed: toRemove, updated, thresholdSurpassed: surpassed };
      if (me.isFiltered && !me.remoteFilter) {
        me.filter({
          silent: me.isRemoteDataLoading
        });
      }
      if (me.isGrouped) {
        me.group(null, null, false, true, me.isRemoteDataLoading);
      } else if (me.isSorted) {
        if (me.remoteSort) {
          storage.replaceValues({
            values: storage.values.sort(me.createSorterFn(me.sorters)),
            silent: true
          });
        } else {
          me.sort();
        }
      } else if (me.reorderOnSync) {
        if (!me.tree) {
          if (storage.values.some((record, index) => ids[record.id] !== index)) {
            storage.replaceValues({
              values: storage.values.sort((a, b) => ids[a.id] - ids[b.id]),
              silent: true
            });
            !surpassed && me.afterPerformSort();
          }
        } else {
          let unsorted = false, i = 0;
          WalkHelper.preWalk(
            me.rootNode,
            (n) => Array.isArray(n.children) && !unsorted ? n.children : null,
            (node) => {
              if (node.id !== ids[i++]) {
                unsorted = true;
              }
            }
          );
          if (unsorted) {
            me.sort((a, b) => ids[a.id] - ids[b.id], void 0, void 0, true);
            me.clearSorters(true);
            !surpassed && me.afterPerformSort();
          }
        }
      }
      if (surpassed) {
        me.trigger("refresh", {
          action: "batch",
          data,
          records: storage.values,
          syncInfo: event
        });
      }
      me.isSyncingDataOnLoad = false;
      me.trigger("endApplyChangeset");
      me.trigger("loadSync", event);
    }
    // Used by syncDataset()
    syncFlatDataset(data, idsToCheck) {
      if (!data) {
        return {
          toRemove: this.records
        };
      }
      const me = this, { idField, allFields } = me.modelClass, toRemove = [], toAdd = [], updated = [], usedIds = {}, ids = {}, limitedSet = idsToCheck instanceof Set;
      const { threshold, keepMissingValues } = me.syncDataOnLoad;
      let hitCount = 0;
      data.forEach((rawData, i) => {
        var _a6, _b2;
        rawData = rawData.isModel ? rawData.data : rawData;
        const id2 = rawData[idField], record = me.getById(id2);
        if (!limitedSet || idsToCheck.has(id2)) {
          if (record) {
            if (me.shouldSyncRecord ? me.shouldSyncRecord({ record, data: rawData, store: me }) !== false : rawData !== record.data) {
              (_a6 = me.beforeSyncRecord) == null ? void 0 : _a6.call(me, { record, data: rawData });
              if (keepMissingValues === false) {
                for (const field of allFields) {
                  if (!(field.dataSource in rawData) && field.dataSource in record.data) {
                    rawData[field.dataSource] = field.defaultValue;
                  }
                }
              }
              const wasSet = record.set(rawData, null, Boolean(threshold));
              if (wasSet) {
                updated.push({
                  record,
                  wasSet,
                  toSet: rawData
                });
              }
              (_b2 = me.afterSyncRecord) == null ? void 0 : _b2.call(me, { record, data: rawData });
            }
          } else {
            toAdd.push(me.processRecord(me.createRecord(rawData)));
          }
        }
        if (record) {
          hitCount++;
        }
        usedIds[id2] = 1;
        ids[id2] = i;
      });
      if (hitCount < me.allRecords.filter((x) => !x.isSpecialRow).length) {
        if (idsToCheck) {
          for (const id2 of idsToCheck) {
            if (!usedIds[id2]) {
              toRemove.push(me.getById(id2));
            }
          }
        } else {
          me.forEach((record) => {
            if (!usedIds[record.id]) {
              toRemove.push(record);
            }
          }, me, {
            includeFilteredOutRecords: true,
            includeCollapsedGroupRecords: true
          });
        }
      }
      return { toAdd, toRemove, toMove: [], updated, ids };
    }
    // Used by syncDataset()
    syncTreeDataset(data) {
      var _a6;
      if (!data) {
        return {
          toRemove: this.records
        };
      }
      const me = this, {
        idField,
        parentIdField,
        childrenField,
        allFields
      } = me.modelClass, {
        keepMissingValues,
        threshold
      } = (_a6 = me.syncDataOnLoad) != null ? _a6 : {}, toRemove = [], toAdd = [], toMove = [], updated = [], matchedNodes = /* @__PURE__ */ new Set(), ids = {};
      let index = 0;
      if (me.transformFlatData) {
        data = me.treeifyFlatData(data);
      }
      WalkHelper.preWalkWithParent({ isRoot: true, id: me.rootNode.id, children: data }, (n) => n.children, (parent, rawData) => {
        if (parent) {
          const { id: id2, node } = me.resolveSyncNode(rawData);
          if (node) {
            if (me.shouldSyncRecord ? me.shouldSyncRecord({ record: node, data: rawData, store: me }) !== false : rawData !== node.data) {
              let childrenUpdated;
              const oldChildrenValue = node.children;
              if (node.parent.id !== parent[idField]) {
                toMove.push({
                  node,
                  parent,
                  index: parent[childrenField].indexOf(rawData)
                });
              }
              let wasSet;
              if (node !== rawData) {
                if (oldChildrenValue !== true && rawData[childrenField] === true) {
                  node.clearChildren();
                  node.data[childrenField] = node.children = true;
                  delete rawData[childrenField];
                  me.toggleCollapse(node, true);
                  childrenUpdated = true;
                }
                if (keepMissingValues === false) {
                  for (const field of allFields) {
                    if (field.name !== "parentId" && !(field.dataSource in rawData) && field.dataSource in node.data) {
                      rawData[field.dataSource] = field.defaultValue;
                    }
                  }
                }
                if (typeof rawData.expanded === "boolean") {
                  this.toggleCollapse(node, !rawData.expanded);
                }
                wasSet = node.set(rawData, null, Boolean(threshold));
              }
              if (wasSet) {
                updated.push({
                  record: node,
                  wasSet,
                  toSet: rawData
                });
              } else if (childrenUpdated) {
                node.signalNodeChanged({
                  [childrenField]: {
                    value: true,
                    oldValue: oldChildrenValue
                  }
                });
              }
            }
            matchedNodes.add(node);
          } else {
            rawData[parentIdField] = parent[idField];
            if (rawData.isModel) {
              toAdd.push(rawData);
            } else {
              toAdd.push({
                ...rawData,
                ...Array.isArray(rawData[childrenField]) ? { children: [] } : void 0
              });
            }
          }
          ids[id2] = index++;
        }
      });
      if (me.totalCount !== matchedNodes.size) {
        me.traverse((node) => {
          if (!matchedNodes.has(node)) {
            toRemove.push(node);
          }
        });
      }
      return { toAdd, toRemove, toMove, updated, ids };
    }
    // ColumnStore overrides this fn to allow syncing by field & type
    resolveSyncNode(rawData) {
      const id2 = rawData[this.modelClass.idField], node = this.getById(id2);
      return { id: id2, node };
    }
  }, __publicField(_a5, "$name", "StoreSync"), _a5;
};

// ../Core/lib/Core/data/stm/RevisionException.js
var RevisionException = class extends Error {
  constructor(...args) {
    super(...args);
    this.name = "RevisionException";
  }
};
RevisionException._$name = "RevisionException";

// ../Core/lib/Core/data/stm/state/StateBase.js
var throwAbstractMethodCall = () => {
  throw new Error("Abstract method call!");
};
var throwInvalidMethodCall = () => {
  throw new Error("Method cannot be called at this state!");
};
var StateBase = class extends Base {
  canUndo(stm) {
    throwAbstractMethodCall();
  }
  canRedo(stm) {
    throwAbstractMethodCall();
  }
  canCheckoutTo(stm) {
    throwAbstractMethodCall();
  }
  onUndo(stm) {
    throwAbstractMethodCall();
  }
  onRedo(stm) {
    throwAbstractMethodCall();
  }
  onCheckoutTo(stm) {
    throwAbstractMethodCall();
  }
  onCheckoutToHead(stm) {
    throwAbstractMethodCall();
  }
  onStartRevision(stm, revisionId) {
    throwAbstractMethodCall();
  }
  onStartTemporaryRevision(stm, revisionId, sourceTransaction) {
    throwAbstractMethodCall();
  }
  onStopRevision(stm) {
    throwAbstractMethodCall();
  }
  onStartTransaction(stm) {
    throwAbstractMethodCall();
  }
  onStopTransaction(stm) {
    throwAbstractMethodCall();
  }
  onStopTransactionDelayed(stm) {
    throwAbstractMethodCall();
  }
  onRejectTransaction(stm) {
    throwAbstractMethodCall();
  }
  onEnable(stm) {
    throwAbstractMethodCall();
  }
  onDisable(stm) {
    throwAbstractMethodCall();
  }
  onAutoRecordOn(stm) {
    throwAbstractMethodCall();
  }
  onAutoRecordOff(stm) {
    throwAbstractMethodCall();
  }
  onResetQueue(stm) {
    throwAbstractMethodCall();
  }
  onModelUpdate(stm) {
    throwAbstractMethodCall();
  }
  onStoreModelAdd(stm) {
    throwAbstractMethodCall();
  }
  onStoreModelInsert(stm) {
    throwAbstractMethodCall();
  }
  onStoreModelRemove(stm) {
    throwAbstractMethodCall();
  }
  onStoreModelRemoveAll(stm) {
    throwAbstractMethodCall();
  }
  onModelInsertChild(stm) {
    throwAbstractMethodCall();
  }
  onModelRemoveChild(stm) {
    throwAbstractMethodCall();
  }
};
StateBase._$name = "StateBase";

// ../Core/lib/Core/data/stm/Props.js
var STATE_PROP = Symbol("STATE_PROP");
var STORES_PROP = Symbol("STORES_PROP");
var QUEUE_PROP = Symbol("QUEUE_PROP");
var POS_PROP = Symbol("POS_PROP");
var ACTION_QUEUE_PROP = Symbol("ACTION_QUEUE_PROP");
var REVISION_NAMES_PROP = Symbol("REVISION_NAMES_PROP");
var REVISION_QUEUE_PROP = Symbol("REVISION_QUEUE_PROP");
var REVISION_INDEX_PROP = Symbol("REVISION_INDEX_PROP");
var CURRENT_REV_ID_PROP = Symbol("CURRENT_REV_ID_PROP");
var TRANSACTION_PROP = Symbol("TRANSACTION_PROP");
var TRANSACTION_TIMER_PROP = Symbol("TRANSACTION_TIMER_PROP");
var AUTO_RECORD_PROP = Symbol("AUTO_RECORD_PROP");
var IS_APPLYING_STASH = Symbol("IS_APPLYING_STASH");
var PROPS = Object.freeze([
  STATE_PROP,
  STORES_PROP,
  QUEUE_PROP,
  POS_PROP,
  ACTION_QUEUE_PROP,
  REVISION_NAMES_PROP,
  REVISION_QUEUE_PROP,
  REVISION_INDEX_PROP,
  CURRENT_REV_ID_PROP,
  TRANSACTION_PROP,
  TRANSACTION_TIMER_PROP,
  AUTO_RECORD_PROP,
  IS_APPLYING_STASH
]);

// ../Core/lib/Core/data/stm/state/Registry.js
var registry2 = /* @__PURE__ */ new Map();
var registerStmState = (name, state) => {
  registry2.set(name, state);
};
var resolveStmState = (state) => {
  if (typeof state === "string") {
    state = registry2.get(state);
  }
  return state;
};
var Registry_default = {
  registerStmState,
  resolveStmState
};

// ../Core/lib/Core/data/stm/Helpers.js
var resetQueue = (stm, options) => {
  const { undo, redo, revision } = options;
  let newProps;
  if (undo && !redo) {
    newProps = {
      [QUEUE_PROP]: stm[QUEUE_PROP].slice(stm.position),
      [POS_PROP]: 0
    };
  } else if (redo && !undo) {
    newProps = {
      [QUEUE_PROP]: stm[QUEUE_PROP].slice(0, stm.position)
    };
  } else {
    newProps = {
      [QUEUE_PROP]: [],
      [POS_PROP]: 0
    };
  }
  if (revision) {
    newProps = Object.assign(newProps != null ? newProps : {}, {
      [REVISION_NAMES_PROP]: ["base"],
      [REVISION_INDEX_PROP]: 0,
      [REVISION_QUEUE_PROP]: []
    });
  }
  ;
  return [
    newProps,
    () => {
      stm.notifyStoresAboutQueueReset(options);
    }
  ];
};

// ../Core/lib/Core/data/stm/state/DisabledState.js
var DisabledStateClass = class extends StateBase {
  canUndo() {
    return false;
  }
  canRedo() {
    return false;
  }
  canCheckoutTo() {
    return false;
  }
  onUndo() {
    throwInvalidMethodCall();
  }
  onRedo() {
    throwInvalidMethodCall();
  }
  onEnable(stm) {
    return stm.autoRecord ? "autoreadystate" : "readystate";
  }
  onDisable() {
    throwInvalidMethodCall();
  }
  onAutoRecordOn() {
    return {
      [AUTO_RECORD_PROP]: true
    };
  }
  onAutoRecordOff() {
    return {
      [AUTO_RECORD_PROP]: false
    };
  }
  onStartTransaction() {
    throwInvalidMethodCall();
  }
  onStopTransaction() {
    throwInvalidMethodCall();
  }
  onStopTransactionDelayed() {
    throwInvalidMethodCall();
  }
  onRejectTransaction() {
    throwInvalidMethodCall();
  }
  onResetQueue(stm, options) {
    return resetQueue(stm, options);
  }
  onModelUpdate() {
  }
  onModelInsertChild() {
  }
  onModelRemoveChild() {
  }
  onStoreModelAdd() {
  }
  onStoreModelInsert() {
  }
  onStoreModelRemove() {
  }
  onStoreRemoveAll() {
  }
};
var DisabledState = new DisabledStateClass();
var DisabledState_default = DisabledState;
Registry_default.registerStmState("disabledstate", DisabledState);

// ../Core/lib/Core/data/stm/action/ActionBase.js
var ActionBase = class extends Base {
  static get defaultConfig() {
    return {
      // If the action is inversed, undo calls redo and vice versa
      inversed: false
    };
  }
  /**
   * Gets the type of the action (stringified class name).
   * @readonly
   * @property {String}
   */
  get type() {
    return this.constructor.name;
  }
  /**
   * Undoes an action
   */
  undo() {
    throwAbstractMethodCall();
  }
  /**
   * Redoes an action
   */
  redo() {
    throwAbstractMethodCall();
  }
  invert() {
    const action = new this.constructor({ ...this.initialConfig, isUserInput: this.isUserInput, inversed: true }), { undo, redo } = action;
    action.undo = redo;
    action.redo = undo;
    return action;
  }
};
var ACTION_TYPES = {
  ADD: "AddAction",
  INSERT: "InsertAction",
  INSERT_CHILD: "InsertChildAction",
  REMOVE: "RemoveAction",
  REMOVE_ALL: "RemoveAllAction",
  REMOVE_CHILD: "RemoveChildAction",
  UPDATE: "UpdateAction",
  // Here we declare an action type which does not exist in the Core. Ideally we should not do it, however
  // alternative only includes single action and is not worth it.
  EVENT_UPDATE: "EventUpdateAction"
};
ActionBase._$name = "ActionBase";

// ../Core/lib/Core/data/stm/action/UpdateAction.js
var MODEL_PROP = Symbol("MODEL_PROP");
var NEW_DATA_PROP = Symbol("NEW_DATA_PROP");
var OLD_DATA_PROP = Symbol("OLD_DATA_PROP");
var UpdateAction = class extends ActionBase {
  static get defaultConfig() {
    return {
      /**
       * Reference to a model which has been updated.
       *
       * @prp {Core.data.Model}
       * @readonly
       * @default
       */
      model: void 0,
      /**
       * Map of updated properties with new values.
       *
       * @prp {Object}
       * @readonly
       * @typings {{[key: string]:any}}
       * @default
       */
      newData: void 0,
      /**
       * Map of updated properties with old values.
       *
       * @prp {Object}
       * @readonly
       * @typings {{[key: string]:any}}
       * @default
       */
      oldData: void 0,
      isInitialUserAction: false
    };
  }
  get type() {
    return ACTION_TYPES.UPDATE;
  }
  get model() {
    return this[MODEL_PROP];
  }
  set model(value2) {
    this[MODEL_PROP] = value2;
  }
  get newData() {
    return this[NEW_DATA_PROP];
  }
  set newData(value2) {
    this[NEW_DATA_PROP] = { ...value2 };
  }
  get oldData() {
    return this[OLD_DATA_PROP];
  }
  set oldData(value2) {
    this[OLD_DATA_PROP] = { ...value2 };
  }
  undo() {
    const { model, oldData } = this;
    if (model.$) {
      Object.assign(model, oldData);
    }
    model.set(oldData, null, null, null, Boolean(model.$));
  }
  redo() {
    const { model, newData } = this;
    if (model.$) {
      Object.assign(model, newData);
    }
    model.set(newData, null, null, null, Boolean(model.$));
  }
};
__publicField(UpdateAction, "$name", "UpdateAction");
UpdateAction._$name = "UpdateAction";

// ../Core/lib/Core/data/stm/action/InsertChildAction.js
var PARENT_MODEL_PROP = Symbol("PARENT_MODEL_PROP");
var CHILD_MODELS_PROP = Symbol("CHILD_MODELS_PROP");
var INSERT_INDEX_PROP = Symbol("INSERT_INDEX_PROP");
var ORDERED_PRP = Symbol("ORDERED_PROP");
var CONTEXT_PROP = Symbol("CONTEXT_PROP");
var InsertChildAction = class extends ActionBase {
  static get defaultConfig() {
    return {
      /**
       * Reference to a parent model a child model has been added to.
       *
       * @prp {Core.data.Model}
       * @readonly
       * @default
       */
      parentModel: void 0,
      /**
       * Children models inserted.
       *
       * @prp {Core.data.Model[]}
       * @readonly
       * @default
       */
      childModels: void 0,
      /**
       * Index a children models are inserted at
       *
       * @prp {Number}
       * @readonly
       * @default
       */
      insertIndex: void 0,
      /**
       * Reference node in the ordered tree
       *
       * @prp {Core.data.Model}
       * @readonly
       * @default
       * @internal
       */
      orderedBeforeNode: void 0,
      /**
       * Map having children models as keys and values containing previous parent
       * of each model and index at the previous parent.
       *
       * @prp {Object}
       * @readonly
       * @default
       */
      context: void 0,
      stores: void 0
    };
  }
  get type() {
    return ACTION_TYPES.INSERT_CHILD;
  }
  get parentModel() {
    return this[PARENT_MODEL_PROP];
  }
  set parentModel(model) {
    this[PARENT_MODEL_PROP] = model;
  }
  get childModels() {
    return this[CHILD_MODELS_PROP];
  }
  set childModels(models) {
    this[CHILD_MODELS_PROP] = models.slice(0);
  }
  get insertIndex() {
    return this[INSERT_INDEX_PROP];
  }
  set insertIndex(index) {
    this[INSERT_INDEX_PROP] = index;
  }
  get orderedBeforeNode() {
    return this[ORDERED_PRP];
  }
  set orderedBeforeNode(node) {
    this[ORDERED_PRP] = node;
  }
  get context() {
    return this[CONTEXT_PROP];
  }
  set context(ctx) {
    this[CONTEXT_PROP] = ctx;
  }
  get stores() {
    return this[STORES_PROP];
  }
  set stores(stores2) {
    this[STORES_PROP] = stores2;
  }
  undo() {
    const { parentModel, context, childModels } = this, byFromParent = /* @__PURE__ */ new Map(), newlyAdded = /* @__PURE__ */ new Set();
    for (const childModel of childModels) {
      const ctx = context.get(childModel);
      if (!ctx) {
        newlyAdded.add(childModel);
      } else {
        let undoTaskData = byFromParent.get(ctx.parent);
        if (!undoTaskData) {
          undoTaskData = { moveRight: [], moveLeft: [], moveFromAnotherParent: [] };
          byFromParent.set(ctx.parent, undoTaskData);
        }
        if (ctx.parent === parentModel) {
          if (ctx.index > childModel.parentIndex) {
            undoTaskData.moveRight.push({ parent: ctx.parent, model: childModel, index: ctx.index + 1 });
          } else {
            undoTaskData.moveLeft.push({ parent: ctx.parent, model: childModel, index: ctx.index });
          }
        } else {
          undoTaskData.moveFromAnotherParent.push({ parent: ctx.parent, model: childModel, index: ctx.index });
        }
      }
    }
    newlyAdded.forEach((model) => model.parent.removeChild(model));
    for (const undoTaskData of byFromParent.values()) {
      const { moveRight, moveLeft, moveFromAnotherParent } = undoTaskData;
      moveFromAnotherParent.forEach((task) => {
        task.parent.insertChild(task.model, task.index);
      });
      moveLeft.sort((a, b) => a.index - b.index);
      moveRight.sort((a, b) => b.index - a.index);
    }
    for (const undoTaskData of byFromParent.values()) {
      const { moveRight, moveLeft } = undoTaskData;
      moveLeft.forEach((task) => {
        task.parent.insertChild(task.model, task.index);
      });
      moveRight.forEach((task) => {
        task.parent.insertChild(task.model, task.index);
      });
    }
  }
  redo() {
    var _a5, _b2;
    const { parentModel, insertIndex, childModels, orderedBeforeNode } = this, insertBefore = (_a5 = parentModel.children) == null ? void 0 : _a5[insertIndex];
    parentModel.insertChild(childModels, insertBefore, false, {
      orderedBeforeNode: orderedBeforeNode != null ? orderedBeforeNode : (_b2 = insertBefore == null ? void 0 : insertBefore.previousSibling) == null ? void 0 : _b2.nextOrderedSibling
    });
  }
};
__publicField(InsertChildAction, "$name", "InsertChildAction");
InsertChildAction._$name = "InsertChildAction";

// ../Core/lib/Core/data/stm/action/RemoveChildAction.js
var PARENT_MODEL_PROP2 = Symbol("PARENT_MODEL_PROP");
var CHILD_MODELS_PROP2 = Symbol("CHILD_MODELS_PROP");
var CONTEXT_PROP2 = Symbol("CONTEXT_PROP");
var RemoveChildAction = class extends ActionBase {
  static get defaultConfig() {
    return {
      /**
       * Reference to a parent model a child model has been removed to.
       *
       * @prp {Core.data.Model}
       * @readonly
       * @default
       */
      parentModel: void 0,
      /**
       * Children models removed.
       *
       * @prp {Core.data.Model[]}
       * @readonly
       * @default
       */
      childModels: void 0,
      /**
       * Map having children models as keys and values containing previous parent
       * index at the parent.
       *
       * @prp {Object}
       * @readonly
       * @default
       */
      context: void 0,
      stores: void 0
    };
  }
  get type() {
    return ACTION_TYPES.REMOVE_CHILD;
  }
  get parentModel() {
    return this[PARENT_MODEL_PROP2];
  }
  set parentModel(model) {
    this[PARENT_MODEL_PROP2] = model;
  }
  get childModels() {
    return this[CHILD_MODELS_PROP2];
  }
  set childModels(models) {
    this[CHILD_MODELS_PROP2] = models.slice(0);
  }
  get context() {
    return this[CONTEXT_PROP2];
  }
  set context(ctx) {
    this[CONTEXT_PROP2] = ctx;
  }
  get stores() {
    return this[STORES_PROP];
  }
  set stores(stores2) {
    this[STORES_PROP] = stores2;
  }
  undo() {
    const { parentModel, context, childModels } = this;
    childModels.sort((lhs, rhs) => {
      const lhsIndex = context.get(lhs).parentIndex, rhsIndex = context.get(rhs).parentIndex;
      return lhsIndex - rhsIndex;
    });
    childModels.forEach((m) => {
      const ctx = context.get(m);
      parentModel.insertChild(m, ctx.parentIndex, void 0, { orderedParentIndex: ctx.orderedParentIndex });
    });
  }
  redo() {
    this.parentModel.removeChild(this.childModels);
  }
};
__publicField(RemoveChildAction, "$name", "RemoveChildAction");
RemoveChildAction._$name = "RemoveChildAction";

// ../Core/lib/Core/data/stm/action/AddAction.js
var STORE_PROP = Symbol("STORE_PROP");
var MODEL_LIST_PROP = Symbol("MODEL_LIST_PROP");
var AddAction = class extends ActionBase {
  static get defaultConfig() {
    return {
      /**
       * Reference to a store models have been added into.
       *
       * @prp {Core.data.Store}
       * @readonly
       * @default
       */
      store: void 0,
      /**
       * List of models added into the store.
       *
       * @prp {Core.data.Model[]}
       * @readonly
       * @default
       */
      modelList: void 0,
      /**
       * Flag showing if undo/redo should be done silently i.e. with events suppressed
       *
       * @prp {Boolean}
       * @readonly
       * @default
       */
      silent: false
    };
  }
  get type() {
    return ACTION_TYPES.ADD;
  }
  get store() {
    return this[STORE_PROP];
  }
  set store(store) {
    this[STORE_PROP] = store;
  }
  get modelList() {
    return this[MODEL_LIST_PROP];
  }
  set modelList(list) {
    this[MODEL_LIST_PROP] = list.slice(0);
  }
  undo() {
    this.store.remove(this.modelList, this.silent);
  }
  redo() {
    this.store.add(this.modelList, this.silent);
  }
};
__publicField(AddAction, "$name", "AddAction");
AddAction._$name = "AddAction";

// ../Core/lib/Core/data/stm/action/InsertAction.js
var STORE_PROP2 = Symbol("STORE_PROP");
var MODEL_LIST_PROP2 = Symbol("MODEL_LIST_PROP");
var INSERT_INDEX_PROP2 = Symbol("INSERT_INDEX_PROP");
var CONTEXT_PROP3 = Symbol("CONTEXT_PROP");
var InsertAction = class extends ActionBase {
  static get defaultConfig() {
    return {
      /**
       * Reference to a store models have been inserted into.
       *
       * @prp {Core.data.Store}
       * @readonly
       * @default
       */
      store: void 0,
      /**
       * List of models inserted into the store.
       *
       * @prp {Core.data.Model[]}
       * @readonly
       * @default
       */
      modelList: void 0,
      /**
       * Index the models have been inserted at.
       *
       * @prp {Number}
       * @readonly
       * @default
       */
      insertIndex: void 0,
      /**
       * Models move context (if models has been moved), if any.
       * Map this {@link Core/data/Model} instances as keys and their
       * previous index as values
       *
       * @prp {Map}
       * @readonly
       * @default
       */
      context: void 0,
      /**
       * Flag showing if undo/redo should be done silently i.e. with events suppressed
       *
       * @prp {Boolean}
       * @readonly
       * @default
       */
      silent: false,
      reportUndoneRemoval: true
    };
  }
  get type() {
    return ACTION_TYPES.INSERT;
  }
  get store() {
    return this[STORE_PROP2];
  }
  set store(store) {
    this[STORE_PROP2] = store;
  }
  get modelList() {
    return this[MODEL_LIST_PROP2];
  }
  set modelList(list) {
    this[MODEL_LIST_PROP2] = list.slice(0);
  }
  get insertIndex() {
    return this[INSERT_INDEX_PROP2];
  }
  set insertIndex(index) {
    this[INSERT_INDEX_PROP2] = index;
  }
  get context() {
    return this[CONTEXT_PROP3];
  }
  set context(context) {
    this[CONTEXT_PROP3] = context;
  }
  undo() {
    const { store, modelList, context, silent, reportUndoneRemoval } = this;
    modelList.sort((lhs, rhs) => {
      const lhsIndex = context.get(lhs), rhsIndex = context.get(rhs);
      return lhsIndex !== void 0 && rhsIndex !== void 0 ? lhsIndex - rhsIndex : 0;
    });
    modelList.forEach((m) => {
      const index = context.get(m);
      m._undoingInsertion = reportUndoneRemoval;
      if (index !== void 0) {
        store.insert(index, m, silent);
      } else {
        store.remove(m, silent);
      }
      m._undoingInsertion = false;
    });
  }
  redo() {
    const me = this;
    me.store.insert(me.insertIndex, me.modelList, me.silent);
  }
};
__publicField(InsertAction, "$name", "InsertAction");
InsertAction._$name = "InsertAction";

// ../Core/lib/Core/data/stm/action/RemoveAction.js
var STORE_PROP3 = Symbol("STORE_PROP");
var MODEL_LIST_PROP3 = Symbol("MODEL_LIST_PROP");
var CONTEXT_PROP4 = Symbol("CONTEXT_PROP");
var RemoveAction = class extends ActionBase {
  static get defaultConfig() {
    return {
      /**
       * Reference to a store models have been removed from.
       *
       * @prp {Core.data.Store}
       * @readonly
       * @default
       */
      store: void 0,
      /**
       * List of models removed from the store.
       *
       * @prp {Core.data.Model[]}
       * @readonly
       * @default
       */
      modelList: void 0,
      /**
       * Models removing context.
       *
       * @prp {Object}
       * @readonly
       * @default
       */
      context: void 0,
      /**
       * Flag showing if undo/redo should be done silently i.e. with events suppressed
       *
       * @prp {Boolean}
       * @readonly
       * @default
       */
      silent: false
    };
  }
  get type() {
    return ACTION_TYPES.REMOVE;
  }
  get store() {
    return this[STORE_PROP3];
  }
  set store(store) {
    this[STORE_PROP3] = store;
  }
  get modelList() {
    return this[MODEL_LIST_PROP3];
  }
  set modelList(list) {
    this[MODEL_LIST_PROP3] = list.slice(0);
  }
  get context() {
    return this[CONTEXT_PROP4];
  }
  set context(context) {
    this[CONTEXT_PROP4] = context;
  }
  undo() {
    const { store, context, modelList, silent } = this;
    modelList.sort((lhs, rhs) => {
      const lhsIndex = context.get(lhs), rhsIndex = context.get(rhs);
      return lhsIndex - rhsIndex;
    });
    modelList.forEach((m) => {
      const index = context.get(m);
      store.insert(index, m, silent);
    });
  }
  redo() {
    this.store.remove(this.modelList, this.silent);
  }
};
__publicField(RemoveAction, "$name", "RemoveAction");
RemoveAction._$name = "RemoveAction";

// ../Core/lib/Core/data/stm/action/RemoveAllAction.js
var STORE_PROP4 = Symbol("STORE_PROP");
var ALL_RECORDS_PROP = Symbol("ALL_RECORDS_PROP");
var RemoveAllAction = class extends ActionBase {
  static get defaultConfig() {
    return {
      /**
       * Reference to a store cleared.
       *
       * @prp {Core.data.Store}
       * @readonly
       * @default
       */
      store: void 0,
      /**
       * All store records removed
       *
       * @prp {Core.data.Model[]}
       * @readonly
       * @default
       */
      allRecords: void 0,
      /**
       * Flag showing if undo/redo should be done silently i.e. with events suppressed
       *
       * @prp {Boolean}
       * @readonly
       * @default
       */
      silent: false
    };
  }
  get type() {
    return ACTION_TYPES.REMOVE_ALL;
  }
  get store() {
    return this[STORE_PROP4];
  }
  set store(store) {
    this[STORE_PROP4] = store;
  }
  get allRecords() {
    return this[ALL_RECORDS_PROP];
  }
  set allRecords(records) {
    this[ALL_RECORDS_PROP] = records.slice(0);
  }
  undo() {
    const { store, allRecords, silent } = this;
    store.add(allRecords, silent);
  }
  redo() {
    this.store.removeAll(this.silent);
  }
};
__publicField(RemoveAllAction, "$name", "RemoveAllAction");
RemoveAllAction._$name = "RemoveAllAction";

// ../Core/lib/Core/data/stm/action/AllActions.js
var makeModelUpdateAction = (model, newData, oldData, isInitialUserAction) => {
  return new UpdateAction({
    model,
    newData,
    oldData,
    isInitialUserAction
  });
};
var makeModelInsertChildAction = (parentModel, insertIndex, childModels, context, orderedBeforeNode, stores2) => {
  return new InsertChildAction({
    parentModel,
    childModels,
    insertIndex,
    orderedBeforeNode,
    context,
    stores: stores2
  });
};
var makeModelRemoveChildAction = (parentModel, childModels, context, stores2) => {
  return new RemoveChildAction({
    parentModel,
    childModels,
    context,
    stores: stores2
  });
};
var makeStoreModelAddAction = (store, modelList, silent) => {
  return new AddAction({
    store,
    modelList,
    silent
  });
};
var makeStoreModelInsertAction = (store, insertIndex, modelList, context, silent) => {
  return new InsertAction({
    store,
    insertIndex,
    modelList,
    context,
    silent
  });
};
var makeStoreModelRemoveAction = (store, modelList, context, silent) => {
  return new RemoveAction({
    store,
    modelList,
    context,
    silent
  });
};
var makeStoreRemoveAllAction = (store, allRecords, silent) => {
  return new RemoveAllAction({
    store,
    allRecords,
    silent
  });
};
var invertAction = (action) => {
  return action.invert();
};

// ../Core/lib/Core/data/stm/Transaction.js
var TRANSACTION_TYPES = {
  DATA_CORRECTION: "DATA_CORRECTION",
  TEMPORARY: "TEMPORARY",
  CONFLICT_RESOLUTION: "CONFLICT_RESOLUTION"
};
var Transaction = class extends Base {
  static get configurable() {
    return {
      /**
       * Transaction title
       *
       * @config {String}
       */
      title: null,
      /**
       * Identifies if revision is temporary or not. Committed revisions get cleaned eventually, local
       * revisions should be committed.
       * @internal
       */
      committed: false,
      inputGeneration: 0
    };
  }
  static from(transactions, config = {}) {
    const result = new this({
      ...this.initialConfig,
      ...config,
      // In theory, here we can lose information about input generation. We are collecting latest generation of
      // actions from each transaction, and we set this value to the max. It means when we are collecting input,
      // we will skip actions with lower generation.
      inputGeneration: transactions.reduce((result2, transaction) => {
        var _a5;
        return Math.max(result2, (_a5 = transaction.inputGeneration) != null ? _a5 : 0);
      }, 0),
      committed: false
    });
    result[ACTION_QUEUE_PROP] = transactions.flatMap((t) => t[ACTION_QUEUE_PROP].filter((a) => {
      if (t.inputGeneration > 0) {
        if (config.conflictResolutionFor) {
          return a.isUserInput === t.inputGeneration;
        } else {
          return a.isUserInput;
        }
      } else {
        return a;
      }
    }));
    return result;
  }
  /**
   * Creates transaction with the lowest generation of actions
   * @param {Core.data.stm.Transaction} transaction
   * @returns {Core.data.stm.Transaction}
   * @internal
   */
  static createTransactionWithOriginalInput(transaction) {
    const result = new this({
      ...this.initialConfig,
      title: transaction.title,
      committed: false
    });
    result[ACTION_QUEUE_PROP] = transaction[ACTION_QUEUE_PROP].filter((a) => a.isUserInput ? a.isUserInput === 1 : !a.isUserInput);
    return result;
  }
  construct(...args) {
    this[ACTION_QUEUE_PROP] = [];
    super.construct(...args);
  }
  /**
   * Gets transaction's actions queue
   *
   * @property {Core.data.stm.action.ActionBase[]}
   */
  get queue() {
    return this[ACTION_QUEUE_PROP].slice(0);
  }
  /**
   * Gets transaction's actions queue length
   *
   * @property {Number}
   */
  get length() {
    return this[ACTION_QUEUE_PROP].length;
  }
  get filterUserInput() {
    return this.inputGeneration > 0;
  }
  /**
   * Adds an action to the transaction.
   *
   * @param {Core.data.stm.action.ActionBase|Object} action
   */
  addAction(action) {
    this[ACTION_QUEUE_PROP].push(action);
  }
  /**
   * Undoes actions held
   */
  undo() {
    const queue = this[ACTION_QUEUE_PROP];
    for (let i = queue.length - 1; i >= 0; --i) {
      queue[i].undo();
    }
  }
  /**
   * Redoes actions held
   */
  redo() {
    const queue = this[ACTION_QUEUE_PROP];
    for (let i = 0, len = queue.length; i < len; ++i) {
      queue[i].redo();
    }
  }
  /**
   * Merges all update actions into one per model, keeping the oldest and the newest values
   */
  mergeUpdateModelActions() {
    var _a5, _b2, _c2;
    const queue = this[ACTION_QUEUE_PROP], recordMap = /* @__PURE__ */ new Map(), keep = [];
    for (const action of queue) {
      if (action.isUpdateAction && ((_a5 = action.model) == null ? void 0 : _a5.isModel)) {
        const index = (_b2 = action.isUserInput) != null ? _b2 : 0;
        let entry = recordMap.get(action.model);
        if (!entry) {
          entry = [];
          recordMap.set(action.model, entry);
        }
        const mergedRecordAction = entry[index];
        if (!mergedRecordAction) {
          entry[index] = action;
          keep.push(action);
        } else {
          for (const key in action.oldData) {
            if (action.newData.hasOwnProperty(key)) {
              if (!mergedRecordAction.oldData.hasOwnProperty(key)) {
                mergedRecordAction.oldData[key] = action.oldData[key];
              }
              mergedRecordAction.newData[key] = action.newData[key];
            }
          }
        }
      } else {
        keep.push(action);
        if (action.isRemoveAction && ((_c2 = action.modelList) == null ? void 0 : _c2.length)) {
          for (const model of action.modelList) {
            recordMap.delete(model);
          }
        }
      }
    }
    this[ACTION_QUEUE_PROP] = keep;
    this.inputGeneration = Math.max(...keep.map((a) => {
      var _a6;
      return (_a6 = a.isUserInput) != null ? _a6 : 0;
    }), 0);
  }
  // This is required to make task editor work with revisions and undo/redo. Some
  // models (dependency base model) contain code which prevents undo from adding record
  // back to the `added` store bag which does not allow revisions feature to be aware
  // of the change. To fix this we break the contract of the STM transaction and remove
  // update actions within one transaction which update added record.
  // The reasoning is that insert/add action will already contain reference to the actual
  // model, therefore there's no need to roll back field modifications. This assumption
  // seem safe.
  mergeAddUpdateModelActions() {
    const queue = this[ACTION_QUEUE_PROP], addedRecords = /* @__PURE__ */ new Set(), keep = [];
    for (const action of queue) {
      if (action.isAddAction || action.isInsertAction) {
        action.modelList.forEach((r) => addedRecords.add(r));
      } else if (action.isInsertChildAction) {
        action.childModels.forEach((r) => addedRecords.add(r));
      }
      if (action.isUpdateAction || action.isEventUpdateAction) {
        if (!addedRecords.has(action.model)) {
          keep.push(action);
        }
      } else {
        keep.push(action);
      }
    }
    this[ACTION_QUEUE_PROP] = keep;
    this.inputGeneration = Math.max(...keep.map((a) => {
      var _a5;
      return (_a5 = a.isUserInput) != null ? _a5 : 0;
    }), 0);
  }
  invert() {
    const transaction = new this.constructor({ ...this.initialConfig, title: this.title });
    transaction[ACTION_QUEUE_PROP] = this[ACTION_QUEUE_PROP].map((action) => invertAction(action));
    return transaction;
  }
  /**
   * Returns a map of actions grouped by `isUserInput` value
   * @returns {Map}
   * @internal
   */
  groupUserInput() {
    return this[ACTION_QUEUE_PROP].reduce((result, action) => {
      var _a5;
      const inputGroup = (_a5 = action.isUserInput) != null ? _a5 : "default";
      if (!result.has(inputGroup)) {
        result.set(inputGroup, []);
      }
      result.get(inputGroup).push(action);
      return result;
    }, /* @__PURE__ */ new Map());
  }
  /**
   * Collects all updates from the transaction into a map with model as key and changed data as value.
   * @param {Number} [generation] If undefined, last generation is returned
   * @returns {Object|undefined}
   * @internal
   */
  getUserInput(generation) {
    const updated = /* @__PURE__ */ new Map(), changes = {}, actions5 = this.groupUserInput().get(generation != null ? generation : this.inputGeneration || "default"), getBagForStore = (name, storeId) => {
      if (!(storeId in changes)) {
        changes[storeId] = { [name]: [] };
      } else if (!(name in changes[storeId])) {
        changes[storeId][name] = [];
      }
      return changes[storeId][name];
    };
    for (const action of actions5) {
      switch (action.type) {
        case ACTION_TYPES.ADD:
        case ACTION_TYPES.INSERT: {
          if (action.inversed) {
            const removed = getBagForStore("removed", action.store.id);
            removed.push(...action.modelList);
          } else {
            const added = getBagForStore("added", action.store.id);
            added.push(...action.modelList);
          }
          break;
        }
        case ACTION_TYPES.REMOVE:
        case ACTION_TYPES.REMOVE_ALL: {
          if (action.inversed) {
            const added = getBagForStore("added", action.store.id);
            added.push(...action.modelList || action.allRecords);
          } else {
            const removed = getBagForStore("removed", action.store.id);
            removed.push(...action.modelList || action.allRecords);
          }
          break;
        }
        case ACTION_TYPES.INSERT_CHILD: {
          const processInsertedSubtree = (record, store) => {
            var _a5;
            if (store.added.includes(record)) {
              getBagForStore("added", store.id).push(record);
            } else if (store.removed.includes(record)) {
              getBagForStore("removed", store.id).push(record);
            }
            (_a5 = record.children) == null ? void 0 : _a5.forEach((child) => processInsertedSubtree(child, store));
          };
          for (const store of action.stores) {
            action.childModels.forEach((record) => processInsertedSubtree(record, store));
            store.modified.forEach((record) => {
              const treeChanges = record.hierarchyModificationDataToWrite;
              if (treeChanges) {
                if (updated.has(record)) {
                  updated.set(record, { ...updated.get(record), ...treeChanges });
                } else {
                  updated.set(record, treeChanges);
                }
              }
            });
          }
          break;
        }
        case ACTION_TYPES.REMOVE_CHILD: {
          let traverse = function(records, output) {
            records.forEach((record) => {
              output.push(record);
              if (record.children) {
                traverse(record.children, output);
              }
            });
          };
          for (const store of action.stores) {
            if (action.inversed) {
              const added = getBagForStore("added", store.id);
              traverse(action.childModels, added);
            } else {
              const removed = getBagForStore("removed", store.id);
              traverse(action.childModels, removed);
            }
          }
          break;
        }
        case ACTION_TYPES.UPDATE:
        case ACTION_TYPES.EVENT_UPDATE: {
          if (updated.has(action.model)) {
            updated.set(action.model, { ...updated.get(action.model), ...action.newData });
          } else {
            updated.set(action.model, { ...action.newData });
          }
          break;
        }
        default:
          break;
      }
    }
    if (updated.size > 0) {
      changes.updated = updated;
    }
    if (Object.keys(changes).length > 0) {
      return changes;
    }
  }
  markCurrentTransactionContentUserInput() {
    const generation = this.inputGeneration + 1, queue = this[ACTION_QUEUE_PROP];
    let generationAssigned = false;
    queue.forEach((action) => {
      if (action.isUserInput === void 0 && !action.isCalculated) {
        action.isUserInput = generation;
        generationAssigned = true;
      }
    });
    if (generationAssigned) {
      this.inputGeneration = generation;
    }
  }
  markCurrentTransactionContentCalculated() {
    const generation = this.inputGeneration, queue = this[ACTION_QUEUE_PROP];
    queue.forEach((action) => {
      if (action.isCalculated === void 0 && !action.isUserInput) {
        action.isCalculated = generation;
      }
    });
  }
  normalizeUserInputGeneration() {
    this[ACTION_QUEUE_PROP].forEach((action) => action.isUserInput = this.inputGeneration);
  }
};
Transaction._$name = "Transaction";

// ../Core/lib/Core/data/stm/state/ReadyState.js
var ReadyStateClass = class extends StateBase {
  canUndo(stm) {
    return 0 < stm.position && stm.position <= stm.length;
  }
  canRedo(stm) {
    return 0 <= stm.position && stm.position < stm.length;
  }
  canCheckoutTo(stm, revision) {
    if (!stm.revisionsEnabled) {
      console.warn("Revisions are disabled");
      return false;
    }
    if (!stm[REVISION_NAMES_PROP].includes(revision)) {
      console.warn("Revision is not found");
      return false;
    }
    return true;
  }
  onUndo(stm, steps) {
    let curPos = stm.position;
    const queue = stm[QUEUE_PROP], newPos = Math.max(0, curPos - steps), next = () => {
      stm.notifyStoresAboutStateRestoringStart();
      const undoneTransactions = [];
      while (curPos !== newPos) {
        const transaction = queue[--curPos];
        transaction.undo();
        undoneTransactions.push(transaction);
      }
      return [stm.autoRecord ? "autoreadystate" : "readystate", () => {
        stm.notifyStoresAboutStateRestoringStop({ cause: "undo", transactions: undoneTransactions });
      }];
    };
    return [{
      [STATE_PROP]: "restoringstate",
      [POS_PROP]: newPos
    }, next];
  }
  onRedo(stm, steps) {
    let curPos = stm.position;
    const queue = stm[QUEUE_PROP], newPos = Math.min(queue.length, curPos + steps);
    const next = () => {
      stm.notifyStoresAboutStateRestoringStart();
      const redoneTransactions = [];
      do {
        const transaction = queue[curPos++];
        transaction.redo();
        redoneTransactions.push(transaction);
      } while (curPos !== newPos);
      return [stm.autoRecord ? "autoreadystate" : "readystate", () => {
        stm.notifyStoresAboutStateRestoringStop({ cause: "redo", transactions: redoneTransactions });
      }];
    };
    return [{
      [STATE_PROP]: "restoringstate",
      [POS_PROP]: newPos
    }, next];
  }
  onCheckoutTo(stm, targetRevision) {
    const revisionIndex = stm[REVISION_NAMES_PROP].indexOf(targetRevision), startIndex = Math.min(revisionIndex, stm[REVISION_INDEX_PROP]), endIndex = Math.max(revisionIndex, stm[REVISION_INDEX_PROP]), isBackward = revisionIndex < stm[REVISION_INDEX_PROP];
    let revisions = stm[REVISION_QUEUE_PROP].slice(startIndex, endIndex);
    if (isBackward) {
      revisions = revisions.filter((rev) => !rev.conflictResolutionFor || rev.type === TRANSACTION_TYPES.TEMPORARY).reverse();
      const dummyRevisions = stm[REVISION_QUEUE_PROP].filter((rev, i) => i >= startIndex && rev.dummy);
      dummyRevisions.forEach((rev) => {
        stm[REVISION_QUEUE_PROP].splice(stm[REVISION_QUEUE_PROP].indexOf(rev), 1);
        stm[REVISION_NAMES_PROP].splice(stm[REVISION_NAMES_PROP].indexOf(rev.title), 1);
      });
    }
    return [{
      [STATE_PROP]: "checkoutstate",
      [REVISION_INDEX_PROP]: revisionIndex
    }, () => {
      stm.notifyStoresAboutCheckoutStart();
      for (const revision of revisions) {
        revision[isBackward ? "undo" : "redo"]();
      }
    }];
  }
  onCheckoutToHead(stm) {
    throwInvalidMethodCall();
  }
  onEnable() {
    throwInvalidMethodCall();
  }
  onDisable() {
    return "disabledstate";
  }
  onAutoRecordOn() {
    return {
      [STATE_PROP]: "autoreadystate",
      [AUTO_RECORD_PROP]: true
    };
  }
  onAutoRecordOff() {
    throwInvalidMethodCall();
  }
  onStartTransaction(stm, title) {
    const transaction = new Transaction({ title });
    return [{
      [STATE_PROP]: "recordingstate",
      [TRANSACTION_PROP]: transaction
    }, () => {
      stm.notifyStoresAboutStateRecordingStart(transaction);
    }];
  }
  onStopTransaction() {
    throwInvalidMethodCall();
  }
  onStopTransactionDelayed() {
    throwInvalidMethodCall();
  }
  onRejectTransaction() {
    throwInvalidMethodCall();
  }
  onResetQueue(stm, options) {
    return resetQueue(stm, options);
  }
  onModelUpdate() {
  }
  onModelInsertChild() {
  }
  onModelRemoveChild() {
  }
  onStoreModelAdd() {
  }
  onStoreModelInsert() {
  }
  onStoreModelRemove() {
  }
  onStoreRemoveAll() {
  }
};
var ReadyState = new ReadyStateClass();
var ReadyState_default = ReadyState;
Registry_default.registerStmState("readystate", ReadyState);

// ../Core/lib/Core/data/stm/state/RecordingState.js
var RecordingStateClass = class extends StateBase {
  canUndo() {
    return false;
  }
  canRedo() {
    return false;
  }
  canCheckoutTo() {
    return false;
  }
  onEnable() {
  }
  onDisable(stm) {
    const transaction = stm[TRANSACTION_PROP];
    stm.notifyStoresAboutStateRecordingStop(transaction, { disabled: true });
    return {
      [STATE_PROP]: "disabledstate",
      [TRANSACTION_PROP]: null
    };
  }
  onAutoRecordOn(stm) {
    return [{
      [STATE_PROP]: "autorecordingstate",
      [AUTO_RECORD_PROP]: true
    }, () => {
      stm.stopTransactionDelayed();
    }];
  }
  onAutoRecordOff() {
    throwInvalidMethodCall();
  }
  onStartTransaction() {
    throwInvalidMethodCall();
  }
  onStopTransaction(stm, title) {
    const transaction = stm[TRANSACTION_PROP], queue = stm[QUEUE_PROP];
    let position = stm[POS_PROP];
    if (transaction.length) {
      if (!transaction.title && !title && stm.getTransactionTitle) {
        transaction.title = stm.getTransactionTitle(transaction);
      } else if (title) {
        transaction.title = title;
      }
      queue[position] = transaction;
      queue.length = ++position;
    }
    return [{
      [STATE_PROP]: "readystate",
      [POS_PROP]: position,
      [TRANSACTION_PROP]: null
    }, () => {
      stm.notifyStoresAboutStateRecordingStop(transaction, { stop: true });
    }];
  }
  onRejectTransaction(stm) {
    const transaction = stm[TRANSACTION_PROP];
    return [{
      [STATE_PROP]: "restoringstate",
      [TRANSACTION_PROP]: null
    }, () => {
      if (transaction.length) {
        transaction.undo();
      }
      return [
        "readystate",
        () => {
          stm.notifyStoresAboutStateRecordingStop(transaction, { rejected: true });
        }
      ];
    }];
  }
  onStopTransactionDelayed() {
    throwInvalidMethodCall();
  }
  onResetQueue(stm, options) {
    return resetQueue(stm, options);
  }
  onModelUpdate(stm, model, newData, oldData, isInitialUserAction) {
    const transaction = stm[TRANSACTION_PROP];
    transaction.addAction(stm.makeModelUpdateAction(model, newData, oldData, isInitialUserAction));
  }
  onModelInsertChild(stm, parentModel, index, childModels, context, orderedBeforeNode) {
    const transaction = stm[TRANSACTION_PROP], stores2 = parentModel.stores.filter((store) => stm.stores.includes(store));
    transaction.addAction(stm.makeModelInsertChildAction(parentModel, index, childModels, context, orderedBeforeNode, stores2));
  }
  onModelRemoveChild(stm, parentModel, childModels, context) {
    const transaction = stm[TRANSACTION_PROP], stores2 = parentModel.stores.filter((store) => stm.stores.includes(store));
    transaction.addAction(stm.makeModelRemoveChildAction(parentModel, childModels, context, stores2));
  }
  onStoreModelAdd(stm, store, models, silent) {
    const transaction = stm[TRANSACTION_PROP];
    transaction.addAction(stm.makeStoreModelAddAction(store, models, silent));
  }
  onStoreModelInsert(stm, store, index, models, context, silent) {
    const transaction = stm[TRANSACTION_PROP], action = stm.makeStoreModelInsertAction(store, index, models, context, silent);
    action.reportUndoneRemoval = !stm.revisionsEnabled;
    transaction.addAction(action);
  }
  onStoreModelRemove(stm, store, models, context, silent) {
    const transaction = stm[TRANSACTION_PROP];
    transaction.addAction(stm.makeStoreModelRemoveAction(store, models, context, silent));
  }
  onStoreRemoveAll(stm, store, allRecords, silent) {
    const transaction = stm[TRANSACTION_PROP];
    transaction.addAction(stm.makeStoreRemoveAllAction(store, allRecords, silent));
  }
};
var RecordingState = new RecordingStateClass();
var RecordingState_default = RecordingState;
Registry_default.registerStmState("recordingstate", RecordingState);

// ../Core/lib/Core/data/stm/state/RestoringState.js
var RestoringStateClass = class extends StateBase {
  canUndo() {
    return false;
  }
  canRedo() {
    return false;
  }
  canCheckoutTo() {
    return false;
  }
  onUndo() {
    throwInvalidMethodCall();
  }
  onRedo() {
    throwInvalidMethodCall();
  }
  onEnable() {
    throwInvalidMethodCall();
  }
  onDisable() {
    throwInvalidMethodCall();
  }
  onAutoRecordOn() {
    return {
      [AUTO_RECORD_PROP]: true
    };
  }
  onAutoRecordOff() {
    return {
      [AUTO_RECORD_PROP]: false
    };
  }
  onStartTransaction() {
    throwInvalidMethodCall();
  }
  onStopTransaction() {
    throwInvalidMethodCall();
  }
  onStopTransactionDelayed() {
    throwInvalidMethodCall();
  }
  onRejectTransaction() {
    throwInvalidMethodCall();
  }
  onQueueReset() {
    throwInvalidMethodCall();
  }
  onModelUpdate() {
  }
  onModelInsertChild() {
  }
  onModelRemoveChild() {
  }
  onStoreModelAdd() {
  }
  onStoreModelInsert() {
  }
  onStoreModelRemove() {
  }
  onStoreRemoveAll() {
  }
};
__publicField(RestoringStateClass, "$name", "RestoringStateClass");
var RestoringState = new RestoringStateClass();
var RestoringState_default = RestoringState;
Registry_default.registerStmState("restoringstate", RestoringState);

// ../Core/lib/Core/data/stm/state/AutoReadyState.js
var AutoReadyStateClass = class extends ReadyStateClass {
  onAutoRecordOn() {
    throwInvalidMethodCall();
  }
  onAutoRecordOff() {
    return {
      [STATE_PROP]: "readystate",
      [AUTO_RECORD_PROP]: false
    };
  }
  onStartTransaction(stm, title) {
    const transaction = new Transaction({ title });
    return [{
      [STATE_PROP]: "autorecordingstate",
      [TRANSACTION_PROP]: transaction
    }, () => {
      stm.notifyStoresAboutStateRecordingStart(transaction);
      stm.stopTransactionDelayed();
    }];
  }
  onModelUpdate(stm, model, newData, oldData) {
    stm.startTransaction();
    stm.onModelUpdate(model, newData, oldData);
  }
  onModelInsertChild(stm, parentModel, index, childModels, context) {
    stm.startTransaction();
    stm.onModelInsertChild(parentModel, index, childModels, context);
  }
  onModelRemoveChild(stm, parentModel, childModels, context) {
    stm.startTransaction();
    stm.onModelRemoveChild(parentModel, childModels, context);
  }
  onStoreModelAdd(stm, store, models, silent) {
    stm.startTransaction();
    stm.onStoreModelAdd(store, models, silent);
  }
  onStoreModelInsert(stm, store, index, models, context, silent) {
    stm.startTransaction();
    stm.onStoreModelInsert(store, index, models, context, silent);
  }
  onStoreModelRemove(stm, store, models, context, silent) {
    stm.startTransaction();
    stm.onStoreModelRemove(store, models, context, silent);
  }
  onStoreRemoveAll(stm, store, allRecords, silent) {
    stm.startTransaction();
    stm.onStoreRemoveAll(store, allRecords, silent);
  }
};
var AutoReadyState = new AutoReadyStateClass();
var AutoReadyState_default = AutoReadyState;
Registry_default.registerStmState("autoreadystate", AutoReadyState);

// ../Core/lib/Core/data/stm/state/AutoRecordingState.js
var AutoRecordingStateClass = class extends RecordingStateClass.mixin(Delayable_default) {
  onDisable(stm) {
    const transaction = stm[TRANSACTION_PROP], timer = stm[TRANSACTION_TIMER_PROP];
    if (timer) {
      this.clearTimeout(timer);
    }
    stm.notifyStoresAboutStateRecordingStop(transaction, { disabled: true });
    return {
      [STATE_PROP]: "disabledstate",
      [TRANSACTION_PROP]: null,
      [TRANSACTION_TIMER_PROP]: null
    };
  }
  onAutoRecordOn(stm) {
    throwInvalidMethodCall();
  }
  onAutoRecordOff(stm) {
    const timer = stm[TRANSACTION_TIMER_PROP];
    if (timer) {
      this.clearTimeout(timer);
    }
    return {
      [STATE_PROP]: "recordingstate",
      [AUTO_RECORD_PROP]: false,
      [TRANSACTION_TIMER_PROP]: null
    };
  }
  onStopTransaction(stm, title) {
    const transaction = stm[TRANSACTION_PROP], timer = stm[TRANSACTION_TIMER_PROP], queue = stm[QUEUE_PROP];
    let position = stm[POS_PROP];
    if (timer) {
      this.clearTimeout(timer);
    }
    if (transaction.length) {
      if (!transaction.title && !title && stm.getTransactionTitle) {
        transaction.title = stm.getTransactionTitle(transaction);
      } else if (title) {
        transaction.title = title;
      }
      queue[position] = transaction;
      queue.length = ++position;
    }
    return [{
      [STATE_PROP]: "autoreadystate",
      [POS_PROP]: position,
      [TRANSACTION_PROP]: null,
      [TRANSACTION_TIMER_PROP]: null
    }, () => {
      stm.notifyStoresAboutStateRecordingStop(transaction, { stop: true });
    }];
  }
  onStopTransactionDelayed(stm) {
    let timer = stm[TRANSACTION_TIMER_PROP];
    if (timer) {
      this.clearTimeout(timer);
    }
    timer = this.setTimeout(
      () => {
        stm.stopTransaction();
      },
      stm.autoRecordTransactionStopTimeout
    );
    return {
      [STATE_PROP]: AutoRecordingState,
      [TRANSACTION_TIMER_PROP]: timer
    };
  }
  onResetQueue(stm, options) {
    return resetQueue(stm, options);
  }
  onRejectTransaction(stm) {
    const transaction = stm[TRANSACTION_PROP], timer = stm[TRANSACTION_TIMER_PROP];
    if (timer) {
      this.clearTimeout(timer);
    }
    return [{
      [STATE_PROP]: "restoringstate",
      [TRANSACTION_PROP]: null,
      [TRANSACTION_TIMER_PROP]: null
    }, () => {
      if (transaction.length) {
        transaction.undo();
      }
      return [
        "autoreadystate",
        () => {
          stm.notifyStoresAboutStateRecordingStop(transaction, { rejected: true });
        }
      ];
    }];
  }
  onModelUpdate(stm, ...rest) {
    super.onModelUpdate(stm, ...rest);
    stm.stopTransactionDelayed();
  }
  onModelInsertChild(stm, ...rest) {
    super.onModelInsertChild(stm, ...rest);
    stm.stopTransactionDelayed();
  }
  onModelRemoveChild(stm, ...rest) {
    super.onModelRemoveChild(stm, ...rest);
    stm.stopTransactionDelayed();
  }
  onStoreModelAdd(stm, ...rest) {
    super.onStoreModelAdd(stm, ...rest);
    stm.stopTransactionDelayed();
  }
  onStoreModelInsert(stm, ...rest) {
    super.onStoreModelInsert(stm, ...rest);
    stm.stopTransactionDelayed();
  }
  onStoreModelRemove(stm, ...rest) {
    super.onStoreModelRemove(stm, ...rest);
    stm.stopTransactionDelayed();
  }
  onStoreRemoveAll(stm, ...rest) {
    super.onStoreRemoveAll(stm, ...rest);
    stm.stopTransactionDelayed();
  }
};
var AutoRecordingState = new AutoRecordingStateClass();
var AutoRecordingState_default = AutoRecordingState;
Registry_default.registerStmState("autorecordingstate", AutoRecordingState);

// ../Core/lib/Core/data/stm/state/CheckoutState.js
var CheckoutStateClass = class extends ReadyStateClass {
  canUndo(stm) {
    return false;
  }
  canRedo(stm) {
    return false;
  }
  onUndo(stm) {
    throwInvalidMethodCall();
  }
  onRedo(stm) {
    throwInvalidMethodCall();
  }
  onStartRevision(stm, revisionId) {
    const transaction = new Transaction({ title: revisionId });
    return [{
      [STATE_PROP]: "revisionrecordingstate",
      [TRANSACTION_PROP]: transaction
    }, () => {
      stm.notifyStoresAboutRevRecordingStart();
    }];
  }
  onStartTemporaryRevision(stm, revisionId, sourceTransaction) {
    const transaction = new Transaction({ title: revisionId });
    if (sourceTransaction) {
      transaction[ACTION_QUEUE_PROP] = sourceTransaction[ACTION_QUEUE_PROP].slice();
    }
    return [{
      [STATE_PROP]: "temporaryrevisionrecordingstate",
      [TRANSACTION_PROP]: transaction
    }, () => {
      stm.notifyStoresAboutTempRevRecordingStart();
    }];
  }
  onCheckoutToHead(stm) {
    stm.notifyStoresAboutCheckoutToHead({ revision: stm[REVISION_NAMES_PROP][stm[REVISION_NAMES_PROP].length - 1] });
    return stm.autoRecord ? "autoreadystate" : "readystate";
  }
  onStartTransaction(stm) {
    throwInvalidMethodCall();
  }
  onStopTransaction(stm) {
    throwInvalidMethodCall();
  }
  onStopTransactionDelayed(stm) {
    throwInvalidMethodCall();
  }
  onRejectTransaction(stm) {
    throwInvalidMethodCall();
  }
  onEnable(stm) {
    throwInvalidMethodCall();
  }
  onDisable(stm) {
    throwInvalidMethodCall();
  }
  onAutoRecordOn(stm) {
    throwInvalidMethodCall();
  }
  onAutoRecordOff(stm) {
    throwInvalidMethodCall();
  }
  onResetQueue(stm) {
    throwInvalidMethodCall();
  }
  // We should not do anything when we're checking out to different revision
  onModelUpdate() {
  }
  onModelInsertChild() {
  }
  onModelRemoveChild() {
  }
  onStoreModelAdd() {
  }
  onStoreModelInsert() {
  }
  onStoreModelRemove() {
  }
  onStoreRemoveAll() {
  }
};
var CheckoutState = new CheckoutStateClass();
var CheckoutState_default = CheckoutState;
Registry_default.registerStmState("checkoutstate", CheckoutState);

// ../Core/lib/Core/data/stm/state/RevisionRecordingState.js
var RevisionRecordingStateClass = class extends RecordingStateClass {
  canUndo(stm) {
    return false;
  }
  canRedo(stm) {
    return false;
  }
  canCheckoutTo() {
    return false;
  }
  onUndo(stm) {
    throwInvalidMethodCall();
  }
  onRedo(stm) {
    throwInvalidMethodCall();
  }
  onCheckoutToHead(stm) {
    throwInvalidMethodCall();
  }
  onStopRevision(stm, title) {
    const transaction = stm[TRANSACTION_PROP], revisionQueue = stm[REVISION_QUEUE_PROP], revisions = stm[REVISION_NAMES_PROP], position = stm[REVISION_INDEX_PROP] + 1;
    const revision = Transaction.createTransactionWithOriginalInput(transaction);
    revisions.splice(position, 0, revision.title);
    revisionQueue.splice(position - 1, 0, revision);
    stm.commitRevision(revision.title);
    return [{
      [STATE_PROP]: "checkoutstate",
      [REVISION_INDEX_PROP]: position,
      [TRANSACTION_PROP]: null
    }, () => {
      stm.notifyStoresAboutRevRecordingStop(revision);
    }];
  }
  onStartTransaction(stm) {
    throwInvalidMethodCall();
  }
  onStopTransaction(stm) {
    throwInvalidMethodCall();
  }
  onStopTransactionDelayed(stm) {
    throwInvalidMethodCall();
  }
  onRejectTransaction(stm) {
    throwInvalidMethodCall();
  }
  onEnable(stm) {
    throwInvalidMethodCall();
  }
  onDisable(stm) {
    throwInvalidMethodCall();
  }
  onAutoRecordOn(stm) {
    throwInvalidMethodCall();
  }
  onAutoRecordOff(stm) {
    throwInvalidMethodCall();
  }
  onResetQueue(stm) {
    throwInvalidMethodCall();
  }
};
var RevisionRecordingState = new RevisionRecordingStateClass();
var RevisionRecordingState_default = RevisionRecordingState;
Registry_default.registerStmState("revisionrecordingstate", RevisionRecordingState);

// ../Core/lib/Core/data/stm/state/TemporaryRevisionRecordingState.js
var TemporaryRevisionRecordingStateClass = class extends RecordingStateClass {
  canUndo(stm) {
    return false;
  }
  canRedo(stm) {
    return false;
  }
  canCheckoutTo() {
    return false;
  }
  onUndo(stm) {
    throwInvalidMethodCall();
  }
  onRedo(stm) {
    throwInvalidMethodCall();
  }
  onCheckoutToHead(stm) {
    throwInvalidMethodCall();
  }
  onStopRevision(stm, title) {
    const transaction = stm[TRANSACTION_PROP];
    return [{
      [STATE_PROP]: "checkoutstate",
      [TRANSACTION_PROP]: null
    }, () => {
      stm.notifyStoresAboutTempRevRecordingStop(transaction);
    }];
  }
  onStartTransaction(stm) {
    throwInvalidMethodCall();
  }
  onStopTransaction(stm) {
    throwInvalidMethodCall();
  }
  onStopTransactionDelayed(stm) {
    throwInvalidMethodCall();
  }
  onRejectTransaction(stm) {
    throwInvalidMethodCall();
  }
  onEnable(stm) {
    throwInvalidMethodCall();
  }
  onDisable(stm) {
    throwInvalidMethodCall();
  }
  onAutoRecordOn(stm) {
    throwInvalidMethodCall();
  }
  onAutoRecordOff(stm) {
    throwInvalidMethodCall();
  }
  onResetQueue(stm) {
    throwInvalidMethodCall();
  }
};
var TemporaryRevisionRecordingState = new TemporaryRevisionRecordingStateClass();
var TemporaryRevisionRecordingState_default = TemporaryRevisionRecordingState;
Registry_default.registerStmState("temporaryrevisionrecordingstate", TemporaryRevisionRecordingState);

// ../Core/lib/Core/data/stm/StateTrackingManager.js
var stateTransition = (stm, event, ...args) => {
  const oldState = stm.state, newState = event.call(stm[STATE_PROP], stm, ...args);
  if (typeof newState === "string") {
    stm[STATE_PROP] = Registry_default.resolveStmState(newState);
  } else if (newState instanceof StateBase) {
    stm[STATE_PROP] = newState;
  } else if (Array.isArray(newState)) {
    const [state, next] = newState;
    if (typeof state === "string") {
      stm[STATE_PROP] = Registry_default.resolveStmState(state);
    } else if (state instanceof StateBase) {
      stm[STATE_PROP] = state;
    } else if (state && typeof state === "object") {
      stm = Object.assign(stm, state);
      stm[STATE_PROP] = Registry_default.resolveStmState(stm[STATE_PROP]);
    }
    if (typeof next === "function") {
      stateTransition(stm, next, ...args);
    }
  } else if (newState && typeof newState === "object") {
    stm = Object.assign(stm, newState);
    stm[STATE_PROP] = Registry_default.resolveStmState(stm[STATE_PROP]);
  }
  if (oldState !== ReadyState_default && oldState !== AutoReadyState_default && (newState !== ReadyState_default && newState !== AutoReadyState_default)) {
    stm.trigger("ready");
  }
};
var StateTrackingManager = class extends Events_default(Base) {
  static get defaultConfig() {
    return {
      /**
       * Default manager disabled state
       *
       * @config {Boolean}
       * @default
       */
      disabled: true,
      /**
       * Whether to start transaction recording automatically in case the Manager is enabled.
       *
       * In the auto recording mode, the manager waits for the first change in any store being managed and starts
       * a transaction, i.e. records any changes in its monitored stores. The transaction lasts for
       * {@link #config-autoRecordTransactionStopTimeout} and afterward creates one undo/redo step, including all
       * changes in the stores during that period of time.
       *
       * In non auto recording mode you have to call {@link #function-startTransaction} /
       * {@link #function-stopTransaction} to start and end a transaction.
       *
       * **NOTE**, enabling this option, will not enable the STM itself. This should be done with a separate call
       * to {@link #function-enable} method or by assigning `false` to {@link #property-disabled} property. If
       * you don't enable the STM, it will not be tracking changes in the data.
       *
       * If you use the `UndoRedo` widget in your app, it will enable STM automatically, on the `load` event of
       * the project's crud manager (in the gantt case, the project is the crud manager). In other cases, you need
       * to enable STM manually.
       *
       * @config {Boolean}
       * @default
       */
      autoRecord: false,
      /**
       * The transaction duration (in ms) for the auto recording mode {@link #config-autoRecord}
       *
       * @config {Number}
       * @default
       */
      autoRecordTransactionStopTimeout: 100,
      /**
       * By the end of a transaction, with this config set to `true`, all model update actions will be merged to
       * one action per model. Only the initial start value and the last change will be kept.
       *
       * If non auto recording mode, you can call {@link #function-mergeTransactionUpdateActions}.
       *
       * @config {Boolean}
       * @default
       */
      autoRecordMergeUpdateActions: true,
      mergeAddUpdateActions: false,
      /**
       * Store model update action factory
       *
       * @config {Function}
       * @default
       * @private
       */
      makeModelUpdateAction,
      /**
       * Store insert child model action factory.
       *
       * @config {Function}
       * @default
       * @private
       */
      makeModelInsertChildAction,
      /**
       * Store remove child model action factory.
       *
       * @config {Function}
       * @default
       * @private
       */
      makeModelRemoveChildAction,
      /**
       * Store add model action factory.
       *
       * @config {Function}
       * @default
       * @private
       */
      makeStoreModelAddAction,
      /**
       * Store insert model action factory.
       *
       * @config {Function}
       * @default
       * @private
       */
      makeStoreModelInsertAction,
      /**
       * Store remove model action factory.
       *
       * @config {Function}
       * @default
       * @private
       */
      makeStoreModelRemoveAction,
      /**
       * Store remove all models action factory.
       *
       * @config {Function}
       * @default
       * @private
       */
      makeStoreRemoveAllAction,
      /**
       * Function to create a transaction title if none is provided.
       * The function receives a transaction and should return a title.
       *
       * @config {Function}
       * @param {Core.data.stm.Transaction} transaction
       * @returns {String}
       * @default
       */
      getTransactionTitle: null,
      //#region Revision configs
      /**
       * Enables [revision](#Gantt/guides/revisions/overview.md) tracking by STM
       * @config {Boolean}
       * @default
       * @category Revisions
       */
      revisionsEnabled: false,
      asyncUndoRedo: false,
      revisionLocalPrefix: "local-",
      /**
       * Specifies length of the transaction queue when cleanup will be triggered
       * @prp {Number}
       * @default
       * @category Revisions
       * @internal
       */
      revisionQueueMaxLength: 20,
      /**
       * Specifies minimum length of last transactions
       * @prp {Number}
       * @default
       * @category Revisions
       * @internal
       */
      revisionQueueCommittedMinLength: 1
      //#endregion
    };
  }
  construct(...args) {
    Object.assign(this, {
      [STATE_PROP]: ReadyState_default,
      [STORES_PROP]: [],
      [QUEUE_PROP]: [],
      [POS_PROP]: 0,
      [TRANSACTION_PROP]: null,
      [TRANSACTION_TIMER_PROP]: null,
      [AUTO_RECORD_PROP]: false,
      [IS_APPLYING_STASH]: false,
      stashedTransactions: {}
    });
    super.construct(...args);
  }
  /**
   * Gets current state of the manager
   *
   * @property {Core.data.stm.state.StateBase}
   */
  get state() {
    return this[STATE_PROP];
  }
  /**
   * Gets current undo/redo queue position
   *
   * @property {Number}
   */
  get position() {
    return this[POS_PROP];
  }
  /**
   * Gets current undo/redo queue length
   *
   * @property {Number}
   */
  get length() {
    return this[QUEUE_PROP].length;
  }
  /**
   * Gets all the stores registered in STM
   *
   * @property {Core.data.Store[]}
   */
  get stores() {
    return Array.from(this[STORES_PROP]);
  }
  /**
   * Checks if a store has been added to the manager
   *
   * @param  {Core.data.Store} store
   * @returns {Boolean}
   */
  hasStore(store) {
    return this[STORES_PROP].includes(store);
  }
  /**
   * Adds a store instance to the manager
   * ```javascript
   * const stm = new StateTrackingManager({ ... })
   * const store = new Store({ ... });
   * stm.addStore(store);
   * ```
   *
   * @param {Core.data.Store} store
   */
  addStore(store) {
    if (!this.hasStore(store)) {
      this[STORES_PROP].push(store);
      store.stm = this;
      store.forEach((model) => model.stm = this);
      if (store.isTree) {
        store.rootNode.stm = this;
      }
    }
  }
  /**
   * Removes a store from the manager
   *
   * @param {Core.data.Store} store
   */
  removeStore(store) {
    if (this.hasStore(store)) {
      this[STORES_PROP] = this[STORES_PROP].filter((s) => s !== store);
      store.stm = null;
      store.forEach((model) => model.stm = null);
    }
  }
  /**
   * Calls `fn` for each store registered in STM.
   *
   * @param {Function} fn (store, id) => ...
   */
  forEachStore(fn2) {
    this[STORES_PROP].forEach((s) => fn2(s, s.id));
  }
  //#region Disabled state
  /**
   * Get/set manager disabled state
   *
   * @property {Boolean}
   */
  get disabled() {
    return this.state === DisabledState_default;
  }
  set disabled(val) {
    const me = this;
    if (me.disabled !== val) {
      if (val) {
        stateTransition(me, me.state.onDisable, me);
      } else {
        stateTransition(me, me.state.onEnable, me);
      }
      me.trigger("stmDisabled", { disabled: val });
      me.trigger("disabled", { disabled: val });
    }
  }
  get enabled() {
    return !this.disabled;
  }
  /**
   * Enables manager
   */
  enable() {
    this.disabled = false;
  }
  /**
   * Disables manager
   */
  disable() {
    this.disabled = true;
  }
  //#endregion
  /**
   * Checks manager ready state
   * @readonly
   * @property {Boolean}
   */
  get isReady() {
    return this.state === ReadyState_default || this.state === AutoReadyState_default;
  }
  waitForReadiness() {
    return this.await("ready", false);
  }
  /**
   * Checks manager recording state
   * @readonly
   * @property {Boolean}
   */
  get isRecording() {
    return this.state === RecordingState_default || this.state === AutoRecordingState_default;
  }
  /**
   * Checks if STM is restoring a stash
   * @readonly
   * @property {Boolean}
   * @internal
   */
  get isApplyingStash() {
    return this[IS_APPLYING_STASH];
  }
  get shouldRecordAction() {
    return this.enabled && (!this.isRestoringState || this.isApplyingStash) && !this.isCheckingOut;
  }
  /**
   * Gets/sets manager auto record option
   *
   * @property {Boolean}
   */
  get autoRecord() {
    return this[AUTO_RECORD_PROP];
  }
  set autoRecord(value2) {
    const me = this;
    if (me.autoRecord != value2) {
      if (value2) {
        stateTransition(me, me.state.onAutoRecordOn, me);
      } else {
        stateTransition(me, me.state.onAutoRecordOff, me);
      }
    }
  }
  /**
   * Starts undo/redo recording transaction.
   *
   * @param {String} [title]
   */
  startTransaction(title = null) {
    stateTransition(this, this.state.onStartTransaction, title);
  }
  /**
   * Stops undo/redo recording transaction
   *
   * @param {String} [title]
   */
  stopTransaction(title = null) {
    const me = this;
    if (me.autoRecord && me.autoRecordMergeUpdateActions) {
      me.mergeTransactionUpdateActions();
    }
    if (me.mergeAddUpdateActions) {
      me.mergeTransactionAddUpdateActions();
    }
    stateTransition(me, me.state.onStopTransaction, title);
  }
  /**
   * Stops undo/redo recording transaction after {@link #config-autoRecordTransactionStopTimeout} delay.
   *
   * @private
   */
  stopTransactionDelayed() {
    stateTransition(this, this.state.onStopTransactionDelayed);
  }
  /**
   * Rejects currently recorded transaction.
   */
  rejectTransaction() {
    stateTransition(this, this.state.onRejectTransaction);
  }
  /**
   * Gets currently recording STM transaction.
   * @readonly
   * @property {Core.data.stm.Transaction}
   */
  get transaction() {
    return this[TRANSACTION_PROP];
  }
  /**
   * Gets titles of all recorded undo/redo transactions
   * @readonly
   * @property {String[]}
   */
  get queue() {
    return this[QUEUE_PROP].map((t) => t.title);
  }
  get rawQueue() {
    return this[QUEUE_PROP];
  }
  get isRestoringState() {
    return this.state === RestoringState_default;
  }
  /**
   * Gets manager restoring state.
   * @readonly
   * @property {Boolean}
   */
  get isRestoring() {
    return this.state === RestoringState_default || this.isApplyingStash || this.isNavigatingRevisions;
  }
  /**
   * Checks if the manager can undo.
   *
   * @property {Boolean}
   */
  get canUndo() {
    return this.state.canUndo(this);
  }
  /**
   * Checks if the manager can redo.
   *
   * @property {Boolean}
   */
  get canRedo() {
    return this.state.canRedo(this);
  }
  /**
   * Undoes current undo/redo transaction.
   * @param {Number} [steps=1]
   * @returns {Promise} A promise which is resolved when undo action has been performed
   */
  async undo(steps = 1) {
    if (!this.isReady) {
      await this.waitForReadiness();
    }
    stateTransition(this, this.state.onUndo, steps);
  }
  /**
   * Undoes all transactions.
   * @returns {Promise} A promise which is resolved when undo actions has been performed
   */
  async undoAll() {
    if (!this.isReady) {
      await this.waitForReadiness();
    }
    this.undo(this.length);
  }
  /**
   * Redoes current undo/redo transaction.
   *
   * @param {Number} [steps=1]
   * @returns {Promise} A promise which is resolved when redo action has been performed
   */
  async redo(steps = 1) {
    if (!this.isReady) {
      await this.waitForReadiness();
    }
    stateTransition(this, this.state.onRedo, steps);
  }
  /**
   * Redoes all transactions.
   * @returns {Promise} A promise which is resolved when redo actions has been performed
   */
  async redoAll() {
    if (!this.isReady) {
      await this.waitForReadiness();
    }
    this.redo(this.length);
  }
  /**
   * Resets undo/redo queue.
   */
  resetQueue(options = { undo: true, redo: true, revision: true }) {
    stateTransition(this, this.state.onResetQueue, options);
  }
  /**
   * Resets undo queue.
   */
  resetUndoQueue() {
    this.resetQueue({ undo: true });
  }
  /**
   * Resets redo queue.
   */
  resetRedoQueue() {
    this.resetQueue({ redo: true });
  }
  resetRevisionQueue() {
    this.resetQueue({ revision: true });
  }
  //#region Events and handlers
  notifyStoresAboutStateRecordingStart(transaction) {
    this.forEachStore((store) => {
      var _a5;
      return (_a5 = store.onStmRecordingStart) == null ? void 0 : _a5.call(store, this, transaction);
    });
    this.trigger("recordingStart", { stm: this, transaction });
  }
  notifyStoresAboutStateRecordingStop(transaction, reason) {
    const me = this;
    me.forEachStore((store) => {
      var _a5;
      return (_a5 = store.onStmRecordingStop) == null ? void 0 : _a5.call(store, me, transaction, reason);
    });
    me.trigger("recordingStop", { stm: me, transaction, reason });
    if (me.revisionsEnabled && reason.stop) {
      me.increaseRevision(Transaction.from([transaction]));
    }
  }
  notifyStoresAboutStateRestoringStart() {
    this.forEachStore((store) => {
      var _a5;
      return (_a5 = store.onStmRestoringStart) == null ? void 0 : _a5.call(store, this);
    });
    this.trigger("restoringStart", { stm: this });
  }
  /**
   * @param {'undo'|'redo'} cause The cause of the restore, if applicable
   * @param {Core.data.stm.Transaction[]} transactions Interacted
   * @internal
   */
  notifyStoresAboutStateRestoringStop({ cause, transactions }) {
    const me = this;
    me.forEachStore((store) => {
      var _a5;
      return (_a5 = store.onStmRestoringStop) == null ? void 0 : _a5.call(store, me);
    });
    me.trigger("restoringStop", { stm: me, cause, transactions });
    if (me.revisionsEnabled) {
      if (me.asyncUndoRedo) {
        const callback = () => {
          const transaction = Transaction.from(transactions);
          me.increaseRevision(cause === "undo" ? transaction.invert() : transaction);
        };
        me.trigger("increaseRevisionAsync", { callback });
      } else {
        const transaction = Transaction.from(transactions);
        me.increaseRevision(cause === "undo" ? transaction.invert() : transaction);
      }
    }
  }
  notifyStoresAboutQueueReset(options) {
    this.forEachStore((store) => {
      var _a5;
      return (_a5 = store.onStmQueueReset) == null ? void 0 : _a5.call(store, this, options);
    });
    this.trigger("queueReset", { stm: this, options });
  }
  notifyStoresAboutCheckoutStart() {
    this.trigger("checkoutStart", { stm: this });
  }
  notifyStoresAboutRevRecordingStart() {
    this.trigger("revisionRecordingStart", { stm: this });
  }
  notifyStoresAboutTempRevRecordingStart() {
    this.trigger("temporaryRevisionRecordingStart", { stm: this });
  }
  notifyStoresAboutRevRecordingStop(transaction) {
    this.trigger("revisionRecordingStop", { stm: this, revision: transaction.title });
  }
  notifyStoresAboutTempRevRecordingStop(transaction) {
    this.trigger("temporaryRevisionRecordingStop", { stm: this, revision: transaction.title });
  }
  notifyStoresAboutCheckoutToHead({ revision }) {
    this.trigger("checkoutToHead", { stm: this, revision });
  }
  /**
   * Method to call from model STM mixin upon model update
   *
   * @param {Core.data.Model} model
   * @param {Object} newData
   * @param {Object} oldData
   *
   * @private
   */
  onModelUpdate(model, newData, oldData, isInitialUserAction) {
    stateTransition(this, this.state.onModelUpdate, model, newData, oldData, isInitialUserAction);
  }
  /**
   * Method to call from model STM mixin upon tree model child insertion
   *
   * @param {Core.data.Model} parentModel Parent model
   * @param {Number} index Insertion index
   * @param {Core.data.Model[]} childModels Array of models inserted
   * @param {Map} context Map with inserted models as keys and objects with previous parent,
   *                      and index at previous parent.
   * @param {Core.data.Model} [orderedBeforeNode] Reference node in ordered tree
   * @private
   */
  onModelInsertChild(parentModel, index, childModels, context, orderedBeforeNode) {
    stateTransition(this, this.state.onModelInsertChild, parentModel, index, childModels, context, orderedBeforeNode);
  }
  /**
   * Method to call from model STM mixin upon tree model child removal
   *
   * @param {Core.data.Model} parentModel
   * @param {Core.data.Model[]} childModels
   * @param {Map} context
   *
   * @private
   */
  onModelRemoveChild(parentModel, childModels, context) {
    stateTransition(this, this.state.onModelRemoveChild, parentModel, childModels, context);
  }
  /**
   * Method to call from store STM mixin upon store models adding
   *
   * @param {Core.data.Store} store
   * @param {Core.data.Model[]} models
   * @param {Boolean} silent
   *
   * @private
   */
  onStoreModelAdd(store, models, silent) {
    stateTransition(this, this.state.onStoreModelAdd, store, models, silent);
  }
  /**
   * Method to call from store STM mixin upon store models insertion
   *
   * @param {Core.data.Store} store
   * @param {Number} index
   * @param {Core.data.Model[]} models
   * @param {Map} context
   * @param {Boolean} silent
   *
   * @private
   */
  onStoreModelInsert(store, index, models, context, silent) {
    stateTransition(this, this.state.onStoreModelInsert, store, index, models, context, silent);
  }
  /**
   * Method to call from store STM mixin upon store models removal
   *
   * @param {Core.data.Store} store
   * @param {Core.data.Model[]} models
   * @param {Object} context
   * @param {Boolean} silent
   *
   * @private
   */
  onStoreModelRemove(store, models, context, silent) {
    stateTransition(this, this.state.onStoreModelRemove, store, models, context, silent);
  }
  /**
   * Method to call from store STM mixin upon store clear
   *
   * @param {Core.data.Store} store
   * @param {Core.data.Model[]} allRecords
   * @param {Boolean} silent
   *
   * @private
   */
  onStoreRemoveAll(store, allRecords, silent) {
    stateTransition(this, this.state.onStoreRemoveAll, store, allRecords, silent);
  }
  // UI key event handling
  onUndoKeyPress(event) {
    const me = this;
    if (me.enabled) {
      if (event.shiftKey) {
        if (me.canRedo) {
          event.preventDefault();
          me.redo();
        }
      } else if (me.canUndo) {
        event.preventDefault();
        me.undo();
      }
    }
  }
  //#endregion
  stash() {
    const me = this;
    if (me.transaction) {
      const id2 = IdHelper.generateId("_stashedTransactionGeneratedId_");
      me.stashedTransactions[id2] = me.transaction;
      me.rejectTransaction();
      return id2;
    }
  }
  applyStash(id2) {
    const me = this, transaction = me.stashedTransactions[id2];
    me[IS_APPLYING_STASH] = true;
    if (transaction) {
      me.startTransaction(transaction.title);
      transaction.redo();
      delete me.stashedTransactions[id2];
    }
    me[IS_APPLYING_STASH] = false;
  }
  /**
   * Merges all update actions into one per model, keeping the oldest and the newest values.
   */
  mergeTransactionUpdateActions(transaction = this.transaction) {
    transaction.mergeUpdateModelActions();
  }
  mergeTransactionAddUpdateActions(transaction = this.transaction) {
    transaction.mergeAddUpdateModelActions();
  }
  //#region Revisions
  canCheckoutTo(revision) {
    return (this.isReady || this.state === CheckoutState_default) && this.state.canCheckoutTo(this, revision);
  }
  get currentRevision() {
    if (this.revisionsEnabled) {
      return this[REVISION_NAMES_PROP][this[REVISION_INDEX_PROP]];
    }
  }
  get lastRevision() {
    if (this.revisionsEnabled) {
      return this[REVISION_NAMES_PROP][this[REVISION_NAMES_PROP].length - 1];
    }
  }
  get currentRevisionTransaction() {
    if (this.revisionsEnabled) {
      return this[REVISION_QUEUE_PROP][this[REVISION_INDEX_PROP] - 1];
    }
  }
  get lastCommittedRevision() {
    const revisionQueue = this[REVISION_QUEUE_PROP];
    let revision;
    for (let i = revisionQueue.length - 1; i >= 0; i--) {
      if (revisionQueue[i].committed) {
        revision = revisionQueue[i];
        break;
      }
    }
    return revision;
  }
  get lastCommittedRevisionId() {
    var _a5, _b2;
    return (_b2 = (_a5 = this.lastCommittedRevision) == null ? void 0 : _a5.title) != null ? _b2 : this[REVISION_NAMES_PROP][0];
  }
  get localRevisions() {
    return this[REVISION_QUEUE_PROP].slice(this[REVISION_QUEUE_PROP].findIndex((r) => !r.committed));
  }
  get isCheckingOut() {
    return this.state === CheckoutState_default;
  }
  get isRecordingRevision() {
    return this.state === RevisionRecordingState_default;
  }
  get isRecordingTemporaryRevision() {
    return this.state === TemporaryRevisionRecordingState_default;
  }
  get isNavigatingRevisions() {
    return this.state === CheckoutState_default || this.state === RevisionRecordingState_default || this.state === TemporaryRevisionRecordingState_default;
  }
  /**
   * This method sets revision number of the initial data set. It should be called once after loading the data to set
   * a starting point to sync revisions with the backend.
   * @param {String} revision
   * @param {Boolean} [enableAutoRecord]
   * @internal
   */
  initRevision(revision, enableAutoRecord = true) {
    const me = this;
    if (me.revisionsEnabled) {
      me.autoRecord = enableAutoRecord;
      me[REVISION_NAMES_PROP] = [revision];
      me[REVISION_QUEUE_PROP] = [];
      me[REVISION_INDEX_PROP] = 0;
    }
  }
  /**
   * This method takes transaction and creates local revision
   * @param {Core.data.stm.Transaction} transaction
   * @param {Object} [changes] Can be provided to a conflict resolving revision
   * @internal
   */
  increaseRevision(transaction, changes) {
    const me = this;
    let userInput;
    if (!(REVISION_NAMES_PROP in me)) {
      throw new Error("Revisions should be initialized first with `project.initRevisions(<client id>)` call");
    }
    if (!me.revisionsEnabled) {
      return;
    }
    if (me.isNavigatingRevisions && !transaction.type) {
      throw new RevisionException("Cannot add new revision when in the checkout state");
    }
    if (!transaction[ACTION_QUEUE_PROP].length && !transaction.type) {
      return;
    }
    const localRevisionId = IdHelper.generateId(me.revisionLocalPrefix);
    transaction.title = localRevisionId;
    if (me.trigger("beforeRevisionAdd", { localRevisionId }) === false) {
      return;
    }
    const temporaryTransaction = transaction.conflictResolutionFor && me[REVISION_QUEUE_PROP].find(
      (t) => t.type === TRANSACTION_TYPES.TEMPORARY && t.conflictResolutionFor === transaction.conflictResolutionFor
    );
    if (temporaryTransaction) {
      me[REVISION_NAMES_PROP].splice(me[REVISION_NAMES_PROP].indexOf(temporaryTransaction.title), 1, localRevisionId);
      me[REVISION_QUEUE_PROP].splice(me[REVISION_QUEUE_PROP].indexOf(temporaryTransaction), 1, transaction);
    } else {
      me[REVISION_NAMES_PROP].push(localRevisionId);
      me[REVISION_QUEUE_PROP].push(transaction);
    }
    if (!transaction.conflictResolutionFor) {
      me[REVISION_INDEX_PROP] = me[REVISION_NAMES_PROP].length - 1;
    }
    if (transaction.type === TRANSACTION_TYPES.TEMPORARY) {
      return;
    }
    if (transaction.filterUserInput) {
      if (!transaction.conflictResolutionFor) {
        transaction.normalizeUserInputGeneration();
      }
      userInput = transaction.getUserInput();
    }
    if (!changes && me[REVISION_QUEUE_PROP].length > me.revisionQueueMaxLength && !transaction.type) {
      me.cleanUpRevisions();
    }
    const eventParams = { localRevisionId, userInput };
    if (transaction.conflictResolutionFor) {
      eventParams.conflictResolutionFor = transaction.conflictResolutionFor;
      eventParams.revisionChanges = changes;
    }
    me.trigger("revisionAdd", eventParams);
  }
  /**
   * This method creates special data revision which has no input state, only a data correction for a case when
   * applying remote revisions lead to new changes. This revision will be sent, it should be persisted on the backend,
   * and any client applying it should not see any changes after.
   * @internal
   */
  createDataCorrectionTransaction() {
    const transaction = new Transaction({ type: TRANSACTION_TYPES.DATA_CORRECTION });
    this.increaseRevision(transaction);
  }
  /**
   * This method creates special conflict resolution revision which contains only conflict resolution actions. It is
   * linked to the revision with a conflict via `conflictResolutionFor` property. Such revision cannot be rolled back
   * locally because it will introduce the same conflict again.
   * @internal
   */
  createConflictResolutionRevision(conflictResolutionFor, localRevisionId, changes) {
    const { transaction } = this;
    changes = ObjectHelper.clone(changes);
    if (localRevisionId) {
      const revision = this[REVISION_QUEUE_PROP].find((t) => t.conflictResolutionFor === localRevisionId);
      if (revision) {
        revision.conflictResolutionFor = conflictResolutionFor;
      }
    }
    this.increaseRevision(Transaction.from([transaction], {
      conflictResolutionFor: conflictResolutionFor != null ? conflictResolutionFor : transaction.title,
      type: TRANSACTION_TYPES.CONFLICT_RESOLUTION
    }), changes);
  }
  createTemporaryConflictResolutionRevision(conflictResolutionFor, changes) {
    const { transaction } = this;
    this.increaseRevision(Transaction.from([transaction], {
      conflictResolutionFor: conflictResolutionFor != null ? conflictResolutionFor : transaction.title,
      type: TRANSACTION_TYPES.TEMPORARY
    }), changes);
  }
  checkoutTo(revision) {
    if (!this.canCheckoutTo(revision)) {
      return false;
    }
    stateTransition(this, this.state.onCheckoutTo, revision);
  }
  checkoutToLastCommittedRevision() {
    this.checkoutTo(this.lastCommittedRevisionId);
  }
  checkoutToHead() {
    this.checkoutTo(this[REVISION_NAMES_PROP][this[REVISION_NAMES_PROP].length - 1]);
    stateTransition(this, this.state.onCheckoutToHead);
  }
  checkoutToNext() {
    if (this[REVISION_INDEX_PROP] + 1 < this[REVISION_NAMES_PROP].length) {
      return this.checkoutTo(this[REVISION_NAMES_PROP][this[REVISION_INDEX_PROP] + 1]);
    } else {
      return false;
    }
  }
  startRevision(revisionId) {
    stateTransition(this, this.state.onStartRevision, revisionId);
  }
  stopRevision() {
    stateTransition(this, this.state.onStopRevision);
  }
  startTemporaryRevision(revisionId, sourceTransaction) {
    stateTransition(this, this.state.onStartTemporaryRevision, revisionId, sourceTransaction);
  }
  stopTemporaryRevision() {
    stateTransition(this, this.state.onStopRevision);
  }
  /**
   * Commits revision, can assign a new id for the local revision which is already applied, but was
   * finally committed on the backend.
   * @param {String} oldId
   * @param {String/Number} newId
   * @internal
   */
  commitRevision(oldId, newId) {
    const revisions = this[REVISION_NAMES_PROP], revisionQueue = this[REVISION_QUEUE_PROP], transaction = revisionQueue.find((t) => t.title === oldId);
    for (let i = revisionQueue.indexOf(transaction) - 1; i >= 0; i--) {
      if (revisionQueue[i].committed === false && revisionQueue[i].type !== TRANSACTION_TYPES.TEMPORARY && !revisionQueue[i].conflictResolutionFor) {
        throw new RevisionException(`Cannot commit revision because it is preceded by an uncommitted rev id ${revisionQueue[i].title}`, revisionQueue);
      }
    }
    if (newId) {
      if (revisions.includes(newId)) {
        throw new RevisionException(`Proposed revision id (${newId}) already exists`);
      }
      transaction.title = revisions[revisions.indexOf(transaction.title)] = newId;
      revisionQueue.forEach((t) => {
        if (t.type === TRANSACTION_TYPES.TEMPORARY && t.conflictResolutionFor === oldId) {
          t.conflictResolutionFor = newId;
        }
      });
    }
    transaction.committed = true;
  }
  /**
   * Clears committed revisions, we only need to use last one.
   * @internal
   */
  cleanUpRevisions(minimumCommittedLength = this.revisionQueueCommittedMinLength) {
    const revisions = this[REVISION_NAMES_PROP], revisionQueue = this[REVISION_QUEUE_PROP], revsToDelete = [];
    for (let i = 0; i < revisionQueue.length; i++) {
      const rev = revisionQueue[i];
      if (rev.committed) {
        revsToDelete.push(rev);
      } else {
        break;
      }
    }
    revsToDelete.splice(
      // Start removing at position N from the end
      Math.max(0, revsToDelete.length - 1 - minimumCommittedLength),
      // Remove max N or array.length elements
      Math.min(revsToDelete.length, minimumCommittedLength)
    );
    revisionQueue.splice(0, revsToDelete.length);
    revisions.splice(1, revsToDelete.length);
  }
  /**
   * Used to distinguish user input from values calculated by the project. If any actions in the revision are marked
   * as user input then checkout logic will only use user input. If nothing is marked, then revision is not treated
   * specially.
   * @internal
   */
  markCurrentTransactionContentUserInput() {
    if (this.isRecording || this.isRecordingRevision || this.isRecordingTemporaryRevision) {
      this.transaction.markCurrentTransactionContentUserInput();
    }
  }
  markCurrentTransactionContentCalculated() {
    if (this.isRecording || this.isRecordingRevision || this.isRecordingTemporaryRevision) {
      this.transaction.markCurrentTransactionContentCalculated();
    }
  }
  //#endregion
};
__publicField(StateTrackingManager, "$name", "StateTrackingManager");
StateTrackingManager._$name = "StateTrackingManager";

// ../Core/lib/Core/data/stm/mixin/StoreStm.js
var StoreStm_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    changeStm(stm, oldStm) {
      var _a6;
      if ((_a6 = oldStm == null ? void 0 : oldStm.hasStore) == null ? void 0 : _a6.call(oldStm, this)) {
        oldStm.removeStore(this);
      }
      if (stm) {
        if (!stm.isStateTrackingManager) {
          stm = new StateTrackingManager(stm);
        }
        if (!stm.hasStore(this)) {
          stm.addStore(this);
        }
      }
      return stm;
    }
    // Overridden to notify STM about flat add action
    add(records, silent = false, options = {}) {
      let result;
      const { stm } = this;
      if (!this.tree && (stm == null ? void 0 : stm.shouldRecordAction)) {
        result = super.add(records, silent, options);
        if (result == null ? void 0 : result.length) {
          stm.onStoreModelAdd(this, result, silent);
        }
      } else {
        result = super.add(records, silent, options);
      }
      return result;
    }
    // Overridden to notify STM about flat insert action
    insert(index, records, silent = false) {
      let result;
      const { stm } = this;
      if (!this.tree && (stm == null ? void 0 : stm.shouldRecordAction)) {
        const context = (Array.isArray(records) ? records : [records]).reduce(
          (context2, r) => {
            const index2 = r instanceof Model ? this.indexOf(r) : void 0;
            if (index2 !== void 0 && index2 !== -1) {
              context2.set(r, index2);
            }
            return context2;
          },
          /* @__PURE__ */ new Map()
        );
        result = super.insert(index, records);
        if (result == null ? void 0 : result.length) {
          index = this.indexOf(result[0]);
          stm.onStoreModelInsert(this, index, result, context, silent);
        }
      } else {
        result = super.insert(index, records, silent);
      }
      return result;
    }
    // Overridden to notify STM about flat removing action
    remove(recordsOrIds, silent = false, fromRemoveChild) {
      let result;
      const { stm } = this;
      if (!this.tree && (stm == null ? void 0 : stm.shouldRecordAction)) {
        const recordsOrIdsNormalized = (Array.isArray(recordsOrIds) ? recordsOrIds : [recordsOrIds]).map((r) => this.getById(r)).filter((r) => Boolean(r));
        const context = recordsOrIdsNormalized.reduce(
          (context2, r) => {
            const index = this.indexOf(r);
            if (index !== void 0 && index !== -1) {
              context2.set(r, index);
            }
            return context2;
          },
          /* @__PURE__ */ new Map()
        );
        result = super.remove(recordsOrIds, silent, fromRemoveChild);
        if (result == null ? void 0 : result.length) {
          stm.onStoreModelRemove(this, result, context, silent);
        }
      } else {
        result = super.remove(recordsOrIds, silent, fromRemoveChild);
      }
      return result;
    }
    // Overridden to notify STM about flat clear action
    removeAll(silent) {
      const { stm } = this;
      let result;
      if (stm == null ? void 0 : stm.shouldRecordAction) {
        const { tree, rootNode, allRecords } = this, wasNotEmpty = allRecords.length, records = tree ? rootNode.children.slice() : allRecords.slice();
        result = super.removeAll(silent);
        if (wasNotEmpty && this.count === 0) {
          stm.onStoreRemoveAll(this, records, silent);
        }
      } else {
        result = super.removeAll(silent);
      }
      return result;
    }
    beforeApplyChangeset() {
      const { stm, crudManager } = this;
      let shouldResume = false, transactionId = null;
      if (!(crudManager == null ? void 0 : crudManager.applyingChangeset) && (stm == null ? void 0 : stm.enabled) && !stm.isNavigatingRevisions) {
        shouldResume = true;
        if (stm.isRecording) {
          transactionId = stm.stash();
        }
        if (this.ignoreRemoteChangesInSTM) {
          stm.disable();
        } else {
          stm.startTransaction();
        }
      }
      return { shouldResume, transactionId };
    }
    // When applying changes while STM is in the recording state, first we need to discard local changes, then apply
    // changes from remote, then try to apply local changes. This would emulate starting transaction as if data was
    // already in the correct state
    applyChangeset(changes, ...args) {
      const {
        shouldResume,
        transactionId
      } = this.beforeApplyChangeset(), log = super.applyChangeset(changes, ...args);
      this.afterApplyChangeset(shouldResume, transactionId);
      return log;
    }
    afterApplyChangeset(shouldResume, transactionId) {
      if (shouldResume) {
        const { stm } = this;
        if (this.ignoreRemoteChangesInSTM) {
          stm.enable();
        } else {
          stm.stopTransaction();
        }
        stm.applyStash(transactionId);
      }
    }
  }, __publicField(_a5, "$name", "StoreStm"), __publicField(_a5, "configurable", {
    /**
     * Reference to STM manager
     *
     * @config {Core.data.stm.StateTrackingManager}
     * @default
     * @category Advanced
     */
    stm: null,
    /**
     * Set to `false` to not record transaction during `applyChangeset` call
     *
     * @prp {Boolean}
     * @default
     * @category Advanced
     */
    ignoreRemoteChangesInSTM: false
  }), _a5;
};

// ../Core/lib/Core/helper/util/Promissory.js
var Promissory = class {
  static new(fn2) {
    return new this(fn2);
  }
  constructor(fn2) {
    this.promise = new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
    if (fn2) {
      fn2(this);
    }
  }
  catch(fn2) {
    this.promise = this.promise.catch(fn2);
    return this;
  }
  finally(fn2) {
    this.promise = this.promise.finally(fn2);
    return this;
  }
  then(...args) {
    this.promise = this.promise.then(...args);
    return this;
  }
};
Promissory._$name = "Promissory";

// ../Core/lib/Core/data/plugin/StoreLazyLoadPlugin.js
var StoreLazyLoadPlugin = class extends InstancePlugin.mixin(Delayable_default) {
  static get pluginConfig() {
    return {
      override: ["updateAutoLoad", "performDataRequest"],
      assign: ["load", "isLoading", "lazyGetAt", "unload"],
      after: ["afterLoadData"],
      before: ["setStoreData", "destroy"]
    };
  }
  static get properties() {
    return {
      loadQueue: {}
    };
  }
  // Checks if there is any unresolved loading promises
  get isLoading() {
    return this.client._isLoading || Object.values(this.loadQueue).some((value2) => ObjectHelper.isPromise(value2));
  }
  get storage() {
    return this.client.storage;
  }
  set totalCount(count) {
    this._totalCount = count;
  }
  get totalCount() {
    return this._totalCount;
  }
  // Overridden in TreeStoreLazyLoadPlugin
  setEstimatedTotalCount(count) {
    this.totalCount = count;
  }
  afterConstruct() {
    const me = this, { client } = me;
    if (client.syncDataOnLoad) {
      client.syncDataOnLoad = false;
    }
    me.storeListenersDetacher = client.ion({
      beforeAdd: "internalOnBeforeAdd",
      beforeRemove: "internalOnBeforeAdd",
      change: "internalOnChange",
      commit: "internalOnCommit",
      endApplyChangeset: "internalOnCommit",
      appliedCrudChangesToStore: "internalOnCommit",
      thisObj: me
    });
    if (!client.requestData) {
      client.requestData = me.requestData;
    }
    if (client.autoLoad && !client.readUrl) {
      this.requestAnimationFrame(this.doAutoLoad);
    }
  }
  updateAutoLoad(autoLoad) {
    if (autoLoad && !this.client.readUrl) {
      this.doAutoLoad();
    }
  }
  //region Internal listeners
  internalOnBeforeAdd() {
    if (this.isLoading) {
      throw new Error("Removing or adding is not allowed when a lazy loaded Store is loading");
    }
  }
  // If we have any uncommited changes, we are not allowed to load more data
  internalOnChange() {
    if (!this.$loadLock && (this.client.added.count || this.client.removed.count)) {
      this.$loadLock = new Promissory();
    }
  }
  // Enable the loading of data upon commit
  internalOnCommit() {
    if (!this.client.changes && this.$loadLock) {
      this.$loadLock.resolve();
      this.$loadLock = null;
    }
  }
  //endregion
  // region Store overrides
  // Will catch remote sorting and filtering calls
  performDataRequest() {
    return this.load();
  }
  afterLoadData() {
    if (this.totalCount) {
      this.storage.values.length = this.totalCount;
    }
  }
  setStoreData(data) {
    if (!(data == null ? void 0 : data.length) && this.client.records.some(Boolean)) {
      this.clear();
    }
  }
  // endregion
  clear() {
    this.totalCount = null;
    this.loadQueue = {};
  }
  // Documented in Store.js
  async requestData({ startIndex, count }) {
  }
  // Assigned to store
  lazyGetAt(index) {
    return this.getAt(index);
  }
  /**
   * Overrides the Store's `getAt` function. If the record at the provided `index` is already loaded, that record will
   * be returned instantly. If not, a request for a range of records containing that index will be made and a promise
   * that resolves to the requested record when the load completes.
   * @param index
   * @internal
   * @returns {Promise}
   */
  async getAt(index) {
    var _a5;
    const me = this, {
      loadQueue,
      totalCount
    } = me;
    if (index < 0 || totalCount != null && index > totalCount) {
      return null;
    }
    const record = me.getFromStorage(index);
    if (record) {
      return record;
    }
    await ((_a5 = me.$loadLock) == null ? void 0 : _a5.promise);
    if (me.isDestroyed) {
      return;
    }
    if (loadQueue[index]) {
      await loadQueue[index];
    } else {
      await me.doLazyLoad(me.calculateRange(index));
    }
    return me.getFromStorage(index);
  }
  // Overridden in TreeStoreLazyLoadPlugin
  getFromStorage(index) {
    return this.storage.getAt(index);
  }
  // Given an index, this will return a startIndex and a count to request externally
  calculateRange(index) {
    const {
      totalCount,
      loadQueue,
      chunkSize
    } = this, minStart = Math.max(0, index - chunkSize), maxEnd = (totalCount ? Math.min(index + chunkSize, totalCount) : index + chunkSize) - 1;
    let startIndex = index, endIndex = index;
    while (startIndex > minStart && !loadQueue[startIndex - 1] && !this.getFromStorage(startIndex - 1)) {
      startIndex -= 1;
    }
    while (endIndex < maxEnd && !loadQueue[endIndex + 1] && !this.getFromStorage(endIndex + 1)) {
      endIndex += 1;
    }
    return {
      startIndex,
      count: endIndex - startIndex + 1
    };
  }
  // Takes a startIndex and endIndex and requests the records in that range. Saves the promises in a queue. Also
  // checks whether the range overlaps with any already requested (but not resolved) indexes.
  async doLazyLoad({ isReload, ...params }) {
    const me = this;
    me.triggerLazyLoadStart(arguments[0]);
    if (isReload) {
      me.clear();
      me.$isCrudManager && (params.isReload = true);
    }
    const { loadQueue, client } = me, { startIndex, count } = params, endIndex = startIndex + count - 1, promise = client.requestData(client.readUrl ? params : client.buildRemoteParams(params));
    for (let i = startIndex; i <= endIndex; i++) {
      loadQueue[i] = promise;
    }
    const response = await promise;
    if (!response) {
      return;
    }
    const {
      [me.totalCountProperty]: totalCount,
      [me.dataProperty]: data
    } = response;
    for (let i = startIndex; i <= endIndex; i++) {
      loadQueue[i] = null;
    }
    if (totalCount != null) {
      me.totalCount = totalCount;
    } else if (me.totalCount == null) {
      if (count > data.length) {
        me.setEstimatedTotalCount(startIndex + data.length);
      }
    }
    me.addData(data, startIndex, isReload);
    if (me.totalCount != null) {
      me.storage.values.length = me.totalCount;
    }
    if (isReload) {
      me._isReloading = false;
    }
    me.triggerLazyLoadEnd(arguments[0], data);
  }
  // Adds data to the store
  addData(data, index, isReload) {
    var _a5;
    const { client, storage } = this;
    if (!(data == null ? void 0 : data.length) && !isReload) {
      return;
    }
    this.pauseStm();
    if ((!client.count || isReload) && !index) {
      client.data = data;
      return;
    }
    for (let i = data.length - 1; i >= 0; i--) {
      const row = data[i], existing = client.idRegister[row.id];
      if (existing) {
        existing.setByDataSource(row, true);
        existing.internalClearChanges(false, true, row);
        data.splice(i, 1);
      }
    }
    if (!data.length) {
      return;
    }
    const { values } = storage, records = data.map((r) => client.processRecord(client.createRecord(r), true));
    if (values.length < index) {
      values.length = index;
    }
    if (index == null) {
      index = values.length;
    }
    values.splice(index, records.length, ...records);
    client.joinRecordsToStore(records);
    (_a5 = this.processAddedRecords) == null ? void 0 : _a5.call(this, records);
    client.updateDependentStores("add", records);
    for (const record of records) {
      client.idMap[record.id] = { index, visibleIndex: index, record };
      index += 1;
      storage.addToIndices(record);
    }
    client.trigger("change", {
      action: "lazyload",
      records
    });
    this.resumeStm();
  }
  // If new id exists in collection, remove that record
  checkDuplicates(records) {
    const { storage } = this;
    for (const record of records) {
      const i = storage.indexOf(record.id);
      if (i >= 0) {
        storage.values.splice(i, 1);
      }
    }
  }
  /**
   * Fired when the store starts loading new chunks (the store enters a state of loading). This event will not be
   * triggered if new records are requested when the store already is loading.
   * @event lazyLoadStarted
   * @param {Core.data.Store} source This Store
   * @on-owner
   */
  triggerLazyLoadStart(params) {
    const me = this;
    if (!me.isLoading) {
      if (me.isConfiguring) {
        me.delay(() => me.trigger("lazyLoadStarted", { ...params }), 0);
      } else {
        me.client.trigger("lazyLoadStarted", { ...params });
      }
    }
    me.client.trigger("beforeLazyLoad", { ...params });
  }
  /**
   * Fired when the store finished loading new chunks (the store stops loading). This event will not be triggered
   * if the store has loading requests pending response.
   * @event lazyLoadEnded
   * @param {Core.data.Store} source This Store
   * @on-owner
   */
  triggerLazyLoadEnd(params, data) {
    if (!this.isLoading) {
      this.client.trigger("lazyLoadEnded");
    }
    this.client.trigger("afterLazyLoad", { ...params, data });
  }
  /**
   * Only available if the store is configured as {@link Core.data.Store#config-lazyLoad}. Calling will initiate a
   * load request for 1 chunk of records starting from index 0. If the store has previously been loaded, it will be
   * cleared of all records and all lazy loading cache.
   * @param {Object} [params] An optional object with parameters that will be included in the fetch request (only
   *                          available for {@link Core.data.AjaxStore} configured with a
   *                          {@link Core.data.AjaxStore#config-readUrl}).
   * @on-owner
   * @returns {Promise}
   */
  load(params) {
    const me = this, isReload = !ObjectHelper.isEmpty(me.loadQueue);
    me._loadedOnce = true;
    me._isReloading = isReload;
    return me.doLazyLoad({ isReload, startIndex: 0, count: me.chunkSize, ...params });
  }
  /**
   * Only available if the store is configured as {@link Core.data.Store#config-lazyLoad}.
   *
   * For a flat Store, calling this method will remove the provided records from the store and reload them the next
   * time they are rendered.
   *
   * For a tree Store, calling this method will remove all child nodes of the provided parent nodes and reload the
   * parent nodes the next time they are rendered or expanded.
   * @param {Object} options
   * @param {Core.data.Model|Number|String|Core.data.Model[]|Number[]|String[]} options.records The records to unload. Can be
   *        a record instance, record id, or an array of record instances or ids.
   * @param {Boolean} [options.includeDescendants=false] Only available for a TreeStore. If `true`, all descendant nodes of the
   *        provided records will be unloaded. If `false`, only the provided records will be unloaded.
   * @on-owner
   */
  unload({ records, ...rest }) {
    if (this.$isCrudManager) {
      console.warn("It is not supported to unload records from the Store when using a lazy loaded CrudManager");
      return;
    }
    const { client } = this;
    for (let record of ArrayHelper.asArray(records)) {
      if (!record.isModel) {
        record = client.getById(record);
      }
      this.internalUnloadRecord({ record, ...rest });
    }
    client.trigger("refresh", {
      action: "lazyload"
    });
  }
  internalUnloadRecord({ record }) {
    const { loadQueue, client } = this, { id: id2 } = record, index = client.indexOf(id2);
    if (loadQueue[id2]) {
      loadQueue[id2] = void 0;
    }
    if (index >= 0) {
      client.records[index] = void 0;
      record.unjoinStore(client);
    }
  }
  pauseStm() {
    const { stm } = this.client;
    if ((stm == null ? void 0 : stm.enabled) && stm.autoRecord) {
      this.$stmIsPaused = true;
      stm.disable();
    }
  }
  async resumeStm(waitForPromise) {
    const { stm } = this.client;
    if (this.$stmIsPaused) {
      this.$stmIsPaused = false;
      await waitForPromise;
      stm.disabled && stm.enable();
    }
  }
  doAutoLoad() {
    if (!this._loadedOnce) {
      this.load();
    }
  }
  destroy() {
    var _a5;
    (_a5 = this.storeListenersDetacher) == null ? void 0 : _a5.call(this);
    this.clear();
    super.destroy();
  }
};
__publicField(StoreLazyLoadPlugin, "$name", "StoreLazyLoadPlugin");
__publicField(StoreLazyLoadPlugin, "configurable", {
  // These can be overridden in AjaxStore.
  // Matches the default values of responseTotalProperty and responseDataProperty
  totalCountProperty: "total",
  dataProperty: "data",
  chunkSize: 100
});
StoreLazyLoadPlugin._$name = "StoreLazyLoadPlugin";

// ../Core/lib/Core/data/plugin/TreeStoreLazyLoadPlugin.js
var countChildren = (parent) => {
  var _a5, _b2;
  return (_b2 = (_a5 = parent.children) == null ? void 0 : _a5.reduce((sum, child) => {
    var _a6;
    return sum + ((_a6 = child.remoteChildCount) != null ? _a6 : 0) + 1;
  }, 0)) != null ? _b2 : 0;
};
var legacyIsFullyLoaded = (parent) => countChildren(parent) >= parent.remoteChildCount;
var TreeStoreLazyLoadPlugin_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    static get pluginConfig() {
      var _a6, _b2;
      const config = {};
      for (const prop in super.pluginConfig) {
        config[prop] = [...super.pluginConfig[prop]];
      }
      config.override = (_a6 = config.override) != null ? _a6 : [];
      config.override.push("loadChildren", "getAt");
      const lazyGetAt = (_b2 = config.assign) == null ? void 0 : _b2.indexOf("lazyGetAt");
      if (lazyGetAt >= 0) {
        config.assign.splice(lazyGetAt, 1);
      }
      return config;
    }
    constructor() {
      super(...arguments);
      this.totalCount = 0;
    }
    // Need to overwrite default load, because it expects a promise
    async load(params) {
      const me = this, isReload = me._loadedOnce;
      if (me.client.autoLoad && !isReload) {
        await new Promise((resolve) => me.requestAnimationFrame(resolve));
      }
      me._loadedOnce = true;
      me._isReloading = isReload;
      return me.doLazyLoad({ parent: me.client.rootNode, isReload, startIndex: 0, ...params });
    }
    // Updates a parent's remoteChildCount by adding the specified amount
    // Also updates the parent's parents the whole way up to the rootNode
    changeRemoteChildCount(records, amount, affectedParents) {
      var _a6;
      if (records == null ? void 0 : records.length) {
        const parents = records.map((r) => {
          var _a7, _b2, _c2;
          return (_c2 = r.parent) != null ? _c2 : this.client.getById((_b2 = r.parentId) != null ? _b2 : (_a7 = r.originalData) == null ? void 0 : _a7.parentId);
        });
        while (parents.length) {
          const parent = parents.shift();
          if (parent) {
            if (!parent.isRoot) {
              parents.push(parent.parent);
            }
            parent.set("remoteChildCount", (_a6 = parent.remoteChildCount ? parent.remoteChildCount + amount : parent.children.length) != null ? _a6 : 0, true);
            affectedParents == null ? void 0 : affectedParents.add(parent);
          }
        }
      }
    }
    // When a record is added or removed
    internalOnCommit({ changes }) {
      const me = this, { $loadLock, client } = me;
      if ($loadLock == null ? void 0 : $loadLock.size) {
        [...$loadLock.keys()].forEach((r) => {
          if (!client.added.some((a) => a.parent === r) && !client.removed.some((a) => a.parent === r)) {
            $loadLock.get(r).resolve();
            $loadLock.delete(r);
          }
        });
      }
      if (me.$legacyCountMode) {
        const affectedParents = /* @__PURE__ */ new Set();
        me.changeRemoteChildCount(changes.added, 1, affectedParents);
        me.changeRemoteChildCount(changes.removed, -1, affectedParents);
        affectedParents.forEach((parent) => {
          parent.isFullyLoaded = legacyIsFullyLoaded(parent);
        });
      }
    }
    // If we have any uncommited changes, we are not allowed to load more data
    internalOnChange() {
      const { $loadLock = this.$loadLock = /* @__PURE__ */ new Map() } = this, { added, removed } = this.client;
      if (added.count || removed.count) {
        const parents = [...new Set(added.map((r) => r.parent).concat(removed.map((r) => r.parent)))];
        parents.forEach((parent) => !$loadLock.get(parent) && $loadLock.set(parent, new Promissory()));
      }
    }
    // Implementing the load on demand function
    async loadChildren(parent) {
      return this.doLazyLoad({ parent, startIndex: 0 });
    }
    // Overrides store default getAt
    getAt(index) {
      const record = this.client.records[index];
      record && !this._isReloading && this.loadMoreChildren(record);
      return record;
    }
    async loadMoreChildren(record) {
      var _a6, _b2, _c2, _d;
      const me = this, { parent } = record;
      if (record.expanded && !((_a6 = record.children) == null ? void 0 : _a6.length) && !record.isFullyLoaded && !me.loadQueue[record.id]) {
        me.doLazyLoad({ parent: record, startIndex: 0 });
      }
      if (record === parent.lastChild && !parent.isFullyLoaded && !me.loadQueue[parent.id]) {
        const loadLock = (_b2 = me.$loadLock) == null ? void 0 : _b2.get(parent);
        if (loadLock) {
          const meta = parent.instanceMeta(me.client);
          meta.isLoadingChildren = true;
          await loadLock.promise;
          if (me.isDestroyed) {
            return;
          }
          if (parent.isFullyLoaded) {
            meta.isLoadingChildren = false;
            (_d = (_c2 = me.client).trigger) == null ? void 0 : _d.call(_c2, "refresh", { action: "lazyload" });
            return;
          }
        }
        me.doLazyLoad({ startIndex: record.parentIndex + 1, parent });
      }
    }
    async doLazyLoad({ parent, isReload, ...params }) {
      var _a6, _b2;
      isReload && this.clear();
      const me = this, { client } = me, { id: id2 } = parent;
      if (me.isDestroyed) {
        return;
      }
      parent.instanceMeta(client).isLoadingChildren = true;
      me.triggerLazyLoadStart(arguments[0]);
      const promise = me.loadQueue[id2] = client.requestData({
        ...client.readUrl ? params : client.buildRemoteParams(params),
        parentId: parent.isRoot ? "root" : id2,
        count: me.chunkSize,
        ...me.$isCrudManager && { isReload }
      });
      const {
        [me.totalCountProperty]: totalCount,
        [me.dataProperty]: data,
        ...additionalData
      } = await promise;
      if (me.isDestroyed || me.loadQueue[id2] !== promise) {
        return;
      }
      delete me.loadQueue[id2];
      if (parent.isRoot && data.length && me.$legacyCountMode === void 0) {
        if ((me.$legacyCountMode = data[0].remoteChildCount >= 0) && !VersionHelper.isTestEnv) {
          VersionHelper.deprecate("core", "7.0.0", "remoteChildCount is deprecated in favor of autodetection and the isFullyLoaded field");
        }
      }
      if (parent.isRoot && totalCount >= 0 && me.$legacyCountMode) {
        parent.set("remoteChildCount", totalCount, true);
      }
      me.addData(data, parent, isReload);
      parent.set("isFullyLoaded", (_a6 = additionalData.isFullyLoaded) != null ? _a6 : me.$legacyCountMode ? legacyIsFullyLoaded(parent) : data.length < me.chunkSize, true);
      me.totalCount = client.count;
      if (isReload) {
        me._isReloading = false;
      }
      if (!me.isLoading) {
        (_b2 = client.trigger) == null ? void 0 : _b2.call(client, "refresh", { action: "lazyload" });
      }
      me.triggerLazyLoadEnd(arguments[0], data);
      parent.instanceMeta(client).isLoadingChildren = false;
    }
    addData(data, parent, isReload) {
      const { client, $legacyCountMode } = this, clearFromAdded = [];
      if (!client.modelClass.fieldMap.isFullyLoaded) {
        client.modelClass.addField({ name: "isFullyLoaded", persist: false });
      }
      if ($legacyCountMode) {
        if (!client.modelClass.fieldMap.remoteChildCount) {
          client.modelClass.addField("remoteChildCount");
        }
        if (!data.length) {
          const childCount = countChildren(parent), childCountDiff = parent.remoteChildCount - childCount;
          if (childCountDiff !== 0) {
            parent.set("remoteChildCount", childCount, true);
            this.changeRemoteChildCount([parent], childCountDiff);
          }
        }
      }
      WalkHelper.preWalk({ children: data, $fake: true }, ({ children }) => (children == null ? void 0 : children.length) ? children : null, (record) => {
        var _a6, _b2, _c2;
        const { expanded } = record;
        if (!record.$fake) {
          clearFromAdded.push(record);
        }
        if (expanded != null && !$legacyCountMode || record.children || record.remoteChildCount > 0) {
          (_a6 = this.$removedDescendants) == null ? void 0 : _a6.forEach((d) => {
            var _a7, _b3;
            if (d.parentId === record.id && !((_b3 = (_a7 = record.children) == null ? void 0 : _a7.some) == null ? void 0 : _b3.call(_a7, (c) => c.id === d.id))) {
              record.children.push(d);
              clearFromAdded.push(d);
              if (d.meta.parentWasFullyLoaded) {
                record.isFullyLoaded = true;
              }
            }
          });
          if (expanded === false && !((_b2 = record.children) == null ? void 0 : _b2.length)) {
            record.children = true;
          } else if ($legacyCountMode && ((_c2 = record.children) == null ? void 0 : _c2.length) && !record.isFullyLoaded && legacyIsFullyLoaded(record)) {
            record.isFullyLoaded = true;
          } else if (!record.children) {
            record.children = [];
          }
        }
      });
      if (isReload) {
        client.data = data;
      } else {
        parent.appendChild(data, true);
        client.added.remove(clearFromAdded);
      }
    }
    unload() {
      super.unload(...arguments);
      this.client.trigger("refresh", { action: "lazyload" });
    }
    internalUnloadRecord({ record, includeDescendants, cacheDescendants }) {
      var _a6;
      if (!record.isParent) {
        return;
      }
      const me = this, {
        loadQueue,
        client,
        $removedDescendants = me.$removedDescendants = []
      } = me;
      if ((_a6 = record.children) == null ? void 0 : _a6.length) {
        record.traverse((child) => me.internalUnloadRecord({ record: child, includeDescendants, cacheDescendants: !includeDescendants }), true);
        const children = [...record.children];
        record.clearChildren(true);
        client.removed.remove(children);
        if (cacheDescendants) {
          $removedDescendants.push(...children);
          children[0].meta.parentWasFullyLoaded = record.isFullyLoaded;
        }
      }
      record.isFullyLoaded = false;
      loadQueue[record.id] = void 0;
    }
  }, __publicField(_a5, "$name", "TreeStoreLazyLoadPlugin"), __publicField(_a5, "configurable", {
    chunkSize: 50
  }), _a5;
};

// ../Core/lib/Core/data/Store.js
var dataAddRemoveActions = {
  splice: 1,
  clear: 1
};
var defaultTraverseOptions2 = {
  includeFilteredOutRecords: false,
  includeCollapsedGroupRecords: false
};
var fixTraverseOptions2 = (store, options) => {
  options = options || false;
  if (typeof options === "boolean") {
    options = {
      includeFilteredOutRecords: options,
      includeCollapsedGroupRecords: false
    };
  }
  return options || defaultTraverseOptions2;
};
var useRawDataUnset = Symbol("useRawDataUnset");
var useRawDataDefaults = {
  enabled: true,
  disableDuplicateIdCheck: true,
  disableDefaultValue: false,
  disableTypeConversion: false
};
var useRawDataRemoteDefaults = {
  enabled: true,
  disableDuplicateIdCheck: false,
  disableDefaultValue: false,
  disableTypeConversion: false
};
var _Store = class _Store extends Base.mixin(
  Delayable_default,
  Identifiable_default,
  Events_default,
  Pluggable_default,
  State_default,
  StoreFilter_default,
  StoreChanges_default,
  StoreCRUD_default,
  StoreRelation_default,
  // Private
  StoreSum_default,
  StoreSearch_default,
  StoreSort_default,
  StoreGroup_default,
  StoreChained_default,
  StoreState_default,
  StoreTree_default,
  StoreStm_default,
  StoreSync_default,
  StorePaging_default,
  StoreProxy_default
  // Private for now, thus not mentioned in @mixes block above
) {
  /**
   * Class used to represent records. Defaults to class Model.
   * @member {Core.data.Model} modelClass
   * @typings {typeof Model}
   * @category Records
   */
  //endregion
  //region Events
  /**
   * Fired when a formula defined on a datafield fails to evaluate.
   * @event formulaError
   * @param {Object} event
   * @param {Core.data.Store} source This Store
   * @param {Core.data.Model} record The record with the formula that failed to evaluate.
   * @param {String} field The field name of the field with the formula that failed to evaluate.
   * @param {Error} error The error thrown by the formula evaluation.
   * @param {String} formula The formula that failed to evaluate.
   * @internal
   */
  /**
   * Fired when the id of a record has changed
   * @event idChange
   * @param {Core.data.Store} source This Store
   * @param {Core.data.Model} record Modified record
   * @param {String|Number} oldValue Old id
   * @param {String|Number} value New id
   */
  /**
   * Fired before record is modified in this store.
   * Modification may be vetoed by returning `false` from a handler.
   * @event beforeUpdate
   * @param {Core.data.Store} source This Store
   * @param {Core.data.Model} record Modified record
   * @param {Object} changes Modification data
   */
  /**
   * Fired when a record is modified
   * @event update
   * @param {Core.data.Store} source This Store
   * @param {Core.data.Model} record Modified record
   * @param {Object} changes Modification data
   */
  /**
   * Fired when one of this Store's constituent records is modified while in
   * {@link Core.data.Model#function-beginBatch batched} state. This may be used to keep
   * UIs up to date while "tentative" changes are made to a record which must not be synced with a server.
   * @event batchedUpdate
   * @private
   */
  /**
   * Fired when the root node is set
   * @event rootChange
   * @param {Core.data.Store} source This Store
   * @param {Core.data.Model} oldRoot The old root node.
   * @param {Core.data.Model} rootNode The new root node.
   */
  /**
   * Data in the store was changed. This is a catch-all event which is fired for all changes
   * which take place to the store's data.
   *
   * This includes mutation of individual records, adding and removal of records, as well as
   * setting a new data payload using the {@link #property-data} property, sorting, filtering,
   * and calling {@link Core.data.mixin.StoreCRUD#function-removeAll}.
   *
   * Simple databound widgets may use to the `change` event to refresh their UI without having to add multiple
   * listeners to the {@link #event-update}, {@link Core.data.mixin.StoreCRUD#event-add},
   * {@link Core.data.mixin.StoreCRUD#event-remove}, {@link #event-refresh} and
   * {@link Core.data.mixin.StoreCRUD#event-removeAll} events.
   *
   * A more complex databound widget such as a grid may use the more granular events to perform less
   * destructive updates more appropriate to each type of change. The properties will depend upon the value of the
   * `action` property.
   *
   * @event change
   * @param {Core.data.Store} source This Store.
   * @param {'remove'|'removeAll'|'add'|'clearchanges'|'filter'|'update'|'dataset'|'replace'} action
   * Name of action which triggered the change. May be one of the options listed above
   * @param {Core.data.Model} record Changed record, for actions that affects exactly one record (`'update'`)
   * @param {Core.data.Model[]} records Changed records, passed for all actions except `'removeAll'`
   * @param {Object} changes Passed for the `'update'` action, info on which record fields changed
   */
  // NOTE: When updating params above, also update change event in ProjectModelMixin and dataChange in ProjectConsumer
  /**
   * Data in the store has completely changed, such as by a filter, or sort or load operation.
   * @event refresh
   * @param {Core.data.Store} source This Store.
   * @param {Boolean} batch Flag set to `true` when the refresh is triggered by ending a batch
   * @param {'dataset'|'sort'|'clearchanges'|'filter'|'create'|'update'|'delete'|'group'} action Name of
   * action which triggered the change. May be one of the options listed above.
   */
  /**
   * Fired before any remote request is initiated.
   * @event beforeRequest
   * @param {Core.data.Store} source This Store
   * @param {String} url The URL to which the HTTP request will be sent. This property may be mutated in
   * an event handler *without changing* the base urls configured for this Store.
   * @param {Object} params An object containing key/value pairs that are passed on the request query string
   * @param {Object} body The body of the request to be posted to the server.
   * @param {'create'|'read'|'update'|'delete'|'readPage'} action Action that is making the request, `'create'`,
   * `'read'`, `'update'`, `'delete'` or `'readPage'`
   * @preventable
   * @async
   */
  /**
   * Fired after any remote request has finished whether successfully or unsuccessfully.
   * @event afterRequest
   * @param {Boolean} exception `true`. *Only present if the request triggered an exception.*
   * @param {'create'|'read'|'update'|'delete'|'readPage'} action Action that has finished, `'create'`, `'read'`,
   * `'update'`, `'delete'` or `'readPage'`
   * @param {'network'|'failure'} exceptionType The type of failure, `'network'` or `'server'`. *Only present
   * if the request triggered an exception.*
   * @param {Response} response The `Response` object
   * @param {Object} json The decoded response object if there was no `'network'` exception.
   * @param {Object} params An object containing key/value pairs that were passed on the request query string
   */
  //endregion
  /* break doc comment from next method */
  //region Init
  constructor(...args) {
    super(...args);
    __publicField(this, "crudManager");
    if (this.objectify) {
      return this.initProxy();
    }
  }
  static get properties() {
    return {
      relationCache: {},
      dependentStoreConfigs: /* @__PURE__ */ new Map(),
      addingClean: false
    };
  }
  static get defaultConfig() {
    return {
      /**
       * An array of field definitions used to create a {@link Core.data.Model} (modelClass) subclass. Optional.
       * If the Model already has fields defined, these fields will extend those.
       *
       * ```javascript
       * const store = new Store({
       *     fields : ['name', 'city', 'company'],
       *     data   : [
       *         { id : 1, name : 'Mark', city : 'London', company : 'Cool inc' },
       *         ...
       *     ]
       * });
       * ```
       *
       * See {@link Core.data.Model} for more info on defining fields, changing data source and mapping fields to
       * nested objects.
       *
       * Note that pre-created record instances supplied to the store are kept as is and thus these fields will
       * not apply to them.
       *
       * @config {Array<String|ModelFieldConfig|Core.data.field.DataField>}
       * @category Common
       */
      fields: null,
      /**
       * Raw data to load initially.
       *
       * Expects an array of JavaScript objects, with properties matching store's fields (defined on its
       * {@link #config-modelClass model} or in the {@link #config-fields} config).
       *
       * ```javascript
       * const store = new Store({
       *     data : [
       *         { id : 1, name : 'Linda', city : 'NY' },
       *         { id : 2, name : 'Olivia', city : 'Paris' },
       *         ...
       *     ]
       * });
       * ```
       *
       * @config {Object[]|Core.data.Model[]}
       * @category Common
       * @non-lazy-load
       */
      data: null,
      /**
       * `true` to act as a tree store.
       * @config {Boolean}
       * @category Tree
       */
      tree: null,
      callOnFunctions: true,
      /**
       * A {@link Core/util/Collection}, or Collection config object
       * to use to contain this Store's constituent records.
       * @config {Core.util.Collection|CollectionConfig}
       * @category Advanced
       */
      storage: null,
      /**
       * Specify `false` to prevent loading records without ids, a good practice to enforce when syncing with a
       * backend.
       *
       * By default, Store allows loading records without ids, in which case a generated id will be assigned.
       *
       * @config {Boolean}
       * @default true
       * @category Advanced
       */
      allowNoId: true,
      /**
       * Prevent dynamically subclassing the modelClass. It does so by default to not pollute it when exposing
       * properties. Should rarely need to be used.
       * @config {Boolean}
       * @default false
       * @private
       * @category Advanced
       */
      preventSubClassingModel: null,
      /**
       * Store class to use when creating the store when it is a part of a
       * [CrudManager](https://bryntum.com/products/scheduler/docs/api/Scheduler/data/CrudManager).
       *
       * ```javascript
       * crudManager : {
       *      eventStore {
       *          storeClass : MyEventStore
       *      }
       * }
       * ```
       *
       * @config {Class}
       * @typings {typeof Store}
       * @category Advanced
       */
      storeClass: null
    };
  }
  static get identifiable() {
    return {
      registerGeneratedId: false
    };
  }
  updateAutoLoad(autoLoad) {
    if (autoLoad && !this.readUrl && !this.lazyLoad) {
      this.requestAnimationFrame(this.performDataRequest);
    }
  }
  construct(config = {}) {
    const me = this;
    Object.assign(me, {
      added: new StoreBag(),
      removed: new StoreBag(),
      modified: new StoreBag(),
      idRegister: {},
      internalIdRegister: {},
      oldIdMap: {}
    });
    super.construct(config);
    me.initRelations();
  }
  /**
   * Retrieves/creates a store based on the passed config.
   *
   * | Type              | Result                                                                 |
   * |-------------------|------------------------------------------------------------------------|
   * | Core.data.Store   | Returns supplied store as is                                           |
   * | String            | Retrieves an existing store by id                                      |
   * | Object            | Creates a new store using supplied config object                       |
   * | Object[]          | Creates a new store, populated with records created from supplied data |
   * | Core.data.Model[] | Creates a new store, populated with supplied records                   |
   *
   *
   * @param {Core.data.Store|StoreConfig|String|StoreConfig[]|Core.data.Model[]} config
   * @param {Object} [defaults] Config object to apply when creating a new store for passed data
   * @param {Function} [converterFn] Function called for each data object prior to creating a record from it. The
   * return value is used to create a record.
   * @private
   */
  static from(config, defaults = {}, converterFn = null) {
    if (config && !config.isStore) {
      if (typeof config === "string") {
        config = _Store.getStore(config);
      } else {
        if (Array.isArray(config)) {
          if (converterFn) {
            config = config.map((data) => data.isModel ? data : converterFn(data));
          }
          config = ObjectHelper.assign({}, defaults, { data: config });
        } else {
          config = ObjectHelper.assign({}, defaults, config);
        }
        config = new _Store(config);
      }
    }
    return config;
  }
  equals(other) {
    return other.isStore && other.$master === this.$master;
  }
  doDestroy() {
    var _a5, _b2, _c2, _d;
    const me = this, allRecords = me.registeredRecords;
    (_b2 = (_a5 = me.stm) == null ? void 0 : _a5.removeStore) == null ? void 0 : _b2.call(_a5, me);
    for (let i = allRecords.length - 1, rec; i >= 0; i--) {
      rec = allRecords[i];
      if (!(rec == null ? void 0 : rec.isDestroyed)) {
        rec.unjoinStore(me);
      }
    }
    (_c2 = me._storage) == null ? void 0 : _c2.destroy();
    if (me.isChained) {
      if (!me.masterStore.isDestroying) {
        ArrayHelper.remove(me.masterStore.chainedStores, me);
      }
    } else {
      (_d = me.rootNode) == null ? void 0 : _d.destroy();
    }
    super.doDestroy();
  }
  /**
   * Stops this store from firing events until {@link #function-endBatch} is called. Multiple calls to `beginBatch`
   * stack up, and will require an equal number of `endBatch` calls to resume events.
   *
   * Upon call of {@link #function-endBatch}, a {@link #event-refresh} event is triggered to allow UIs to
   * update themselves based upon the new state of the store.
   *
   * This is extremely useful when making a large number of changes to a store. It is important not to trigger
   * too many UI updates for performance reasons. Batching the changes ensures that UIs attached to this
   * store are only updated once at the end of the updates.
   */
  beginBatch() {
    this.suspendEvents();
  }
  /**
   * Ends event suspension started by {@link #function-beginBatch}. Multiple calls to {@link #function-beginBatch}
   * stack up, and will require an equal number of `endBatch` calls to resume events.
   *
   * Upon call of `endBatch`, a {@link #event-refresh} event with `action: batch` is triggered to allow UIs to update
   * themselves based upon the new state of the store.
   *
   * This is extremely useful when making a large number of changes to a store. It is important not to trigger
   * too many UI updates for performance reasons. Batching the changes ensures that UIs attached to this
   * store are only updated once at the end of the updates.
   */
  endBatch() {
    if (this.resumeEvents()) {
      const { values: records } = this.storage;
      this.trigger("refresh", {
        action: "batch",
        data: records,
        records
      });
    }
  }
  set storage(storage) {
    const me = this;
    if (storage == null ? void 0 : storage.isCollection) {
      me._storage = storage;
    } else {
      me._storage = new Collection(storage);
    }
    me._storage.autoFilter = me.reapplyFilterOnAdd;
    for (const r of me._storage) {
      r.joinStore(me);
    }
    me._storage.ion({
      change: "onDataChange",
      thisObj: me
    });
  }
  get storage() {
    if (!this._storage) {
      this.storage = {};
    }
    return this._storage;
  }
  /**
   * Returns all locally available records from the store, ignoring any filters and including grouping headers / footers.
   * @property {Core.data.Model[]}
   * @readonly
   * @category Records
   */
  get allRecords() {
    var _a5;
    const me = this;
    if (((_a5 = me._allRecords) == null ? void 0 : _a5.generation) !== me.storage.generation) {
      if (me.isTree) {
        const result = me.collectDescendants(me.rootNode, void 0, void 0, { unfiltered: true }).all;
        if (me.rootVisible) {
          result.unshift(me.rootNode);
        }
        me._allRecords = result;
      } else {
        me._allRecords = me.isGrouped ? me.collectGroupRecords(true, true) : me.storage.allValues;
      }
      me._allRecords.generation = me.storage.generation;
    }
    return me._allRecords;
  }
  // All records except special rows such group headers etc
  getAllDataRecords(searchAllRecords) {
    const me = this;
    if (me.tree) {
      return searchAllRecords ? me.allRecords : me.rootNode.allChildren;
    }
    return me.isGrouped ? me.collectGroupRecords(searchAllRecords, false) : (searchAllRecords ? me.storage.allValues : me.storage.values).filter((r) => r);
  }
  /**
   * Called by owned record when the record has its {@link Core.data.Model#property-isCreating}
   * property toggled.
   * @param {Core.data.Model} record The record that is being changed.
   * @param {Boolean} isCreating The new value of the {@link Core.data.Model#property-isCreating} property.
   * @internal
   */
  onIsCreatingToggle(record, isCreating) {
    const me = this, newlyPersistable = record.isPersistable && !isCreating;
    me.added[newlyPersistable ? "add" : "remove"](record);
    if (newlyPersistable) {
      me.trigger("addConfirmed", { record });
      if (me.autoCommit) {
        me.doAutoCommit();
      }
    }
  }
  // Join added records to store, not called when loading
  joinRecordsToStore(records) {
    const { totalCount } = this;
    for (let i = 0; i < records.length; i++) {
      const record = records[i];
      record.setData("parentIndex", totalCount + i - records.length);
      record.joinStore(this);
    }
  }
  /**
   * Responds to mutations of the underlying storage Collection
   * @param {Object} event
   * @protected
   */
  onDataChange({ source: storage, action, added, removed, replaced, oldCount, items: items2, from, to }) {
    const me = this, isAddRemove = dataAddRemoveActions[action], addedCount = isAddRemove && (added == null ? void 0 : added.length), removedCount = isAddRemove && (removed == null ? void 0 : removed.length), { generation } = storage, isLocallyFiltered = me.isFiltered && !me.remoteFilter;
    let filtersWereReapplied, sortersWereReapplied;
    me._idMap = null;
    if (addedCount) {
      me.joinRecordsToStore(added);
    }
    replaced == null ? void 0 : replaced.forEach(([oldRecord, newRecord]) => {
      oldRecord.unjoinStore(me, true);
      newRecord.joinStore(me);
    });
    super.onDataChange(...arguments);
    if (addedCount && storage.generation !== generation && !isLocallyFiltered) {
      me.trigger("refresh", arguments[0]);
    }
    if (!me.isTree) {
      if (addedCount) {
        for (const record of added) {
          if (me.removed.includes(record)) {
            me.removed.remove(record);
          } else if (!me.addingClean && !record.isLinked) {
            me.added.add(record);
          }
        }
        filtersWereReapplied = !me.remoteFilter && me.isFiltered && me.reapplyFilterOnAdd;
        if (filtersWereReapplied) {
          me.filter({
            silent: true
          });
        }
        sortersWereReapplied = !me.remoteSort && me.isSorted && me.reapplySortersOnAdd;
        if (sortersWereReapplied) {
          me.sort(null, null, false, true);
        }
      }
      if (removedCount) {
        for (const record of removed) {
          record.cancelBatch();
          record.unjoinStore(me);
          if (me.added.includes(record)) {
            me.added.remove(record);
            if (record.isBeingMaterialized) {
              me.removed.add(record);
            }
          } else if (!record._undoingInsertion && !record.isCreating && !record.isLinked) {
            me.removed.add(record);
          }
        }
        me.modified.remove(removed);
        filtersWereReapplied = isLocallyFiltered;
        if (filtersWereReapplied) {
          me.filter({
            silent: true
          });
        }
      }
    }
    switch (action) {
      case "clear":
        me.relationCache = {};
        me.updateDependentStores("removeall");
        me.trigger("removeAll");
        me.trigger("change", {
          action: "removeall"
        });
        break;
      case "splice":
        if (addedCount) {
          me.updateDependentStores("add", added);
          const oldIndex = added.reduce((lowest, record) => {
            const { previousIndex } = record.meta;
            if (previousIndex > -1 && previousIndex < lowest)
              lowest = previousIndex;
            return lowest;
          }, added[0].meta.previousIndex), index = storage.indexOf(added[0], !storage.autoFilter), params = {
            records: added,
            index
          };
          if (oldIndex > -1) {
            params.oldIndex = oldIndex;
          }
          me.trigger("add", params);
          me.trigger("change", Object.assign({ action: "add" }, params));
          if (filtersWereReapplied) {
            me.triggerFilterEvent({
              action: "filter",
              filters: me.filters,
              oldCount,
              records: me.storage.allValues
            });
          }
          if (sortersWereReapplied) {
            me.trigger("sort", { action: "sort", sorters: me.sorters, records: me.storage.allValues });
          }
        }
        if (removed.length) {
          me.updateDependentStores("remove", removed);
          me.trigger("remove", {
            records: removed
          });
          me.trigger("change", {
            action: "remove",
            records: removed
          });
        }
        if (replaced.length) {
          me.updateDependentStores("replace", replaced);
          me.trigger("replace", {
            records: replaced,
            all: me.records.length === replaced.length
          });
          me.trigger("change", {
            action: "replace",
            replaced,
            all: me.records.length === replaced.length
          });
        }
        break;
      case "filter":
        if (me.isGrouped || me.isSorted) {
          me.performSort(true);
        }
        break;
      case "move": {
        const start = Math.min(from, to), end = Math.min(me.storage.allValues.length - 1, Math.max(from, to));
        for (let allRecords = me.storage.allValues, i = start; i <= end; i++) {
          allRecords[i].setData("parentIndex", i);
        }
        me.trigger("move", {
          records: items2,
          from,
          to
        });
        if (me.isFiltered) {
          me.performFilter();
        }
        me.trigger("change", {
          action,
          record: items2[0],
          records: items2,
          from,
          to
        });
        break;
      }
    }
  }
  onDataReplaced(action, data) {
    var _a5, _b2;
    const me = this, { storage } = me, all = storage.allValues, sorted = Boolean(me.sorters.length > 0);
    for (let i = 0; i < all.length; i++) {
      (_a5 = all[i]) == null ? void 0 : _a5.joinStore(me);
    }
    if (!me.remoteFilter && me.isFiltered) {
      me.filter({
        silent: true
      });
    }
    if (me.remoteSort) {
      if (me.isGrouped) {
        storage.replaceValues({
          // Need to update group records info (headers and footers)
          ...me.prepareGroupRecords(),
          silent: true
        });
      }
    } else {
      if (me.isGrouped) {
        me.group(null, null, false, !sorted, true);
      }
      if (sorted) {
        me.sort(null, null, false, true);
      }
    }
    if (!me.useRawData.disableDuplicateIdCheck) {
      const { idMap } = me;
      if (Object.keys(idMap).length < storage.values.length) {
        const collisions = [];
        storage.values.forEach((r) => idMap[r.id] ? delete idMap[r.id] : collisions.push(r));
        throw new Error(`Id collision on ${collisions.map((r) => r.id)}`);
      }
    }
    const event = { action, data, records: storage.values };
    me.updateDependentStores(action, event.records);
    (_b2 = me.afterLoadData) == null ? void 0 : _b2.call(me);
    if (!me.isRemoteDataLoading) {
      me.trigger("refresh", event);
    }
    me.trigger("change", event);
  }
  /**
   * This is called from Model after mutating any fields so that Stores can take any actions necessary at that point,
   * and distribute mutation event information through events.
   * @param {Core.data.Model} record The record which has just changed
   * @param {Object} toSet A map of the field names and values that were passed to be set
   * @param {Object} wasSet A map of the fields that were set. Each property is a field name, and
   * the property value is an object containing two properties: `oldValue` and `value` eg:
   *
   * ```javascript
   *     {
   *         name {
   *             oldValue : 'Rigel',
   *             value : 'Nigel'
   *         }
   *     }
   * ```
   *
   * @param {Boolean} silent Do not trigger events
   * @param {Boolean} fromRelationUpdate Update caused by a change in related model
   * @private
   */
  onModelChange(record, toSet, wasSet, silent, fromRelationUpdate) {
    var _a5;
    const me = this, event = {
      record,
      records: [record],
      changes: wasSet,
      // Cannot use isBatching, since change is triggered when batching has reached 0
      // (but before it is set to null)
      batch: record.batching != null,
      fromRelationUpdate
    };
    me.storage.onItemMutation(record, wasSet);
    if ("id" in wasSet) {
      const { oldValue, value: value2 } = toSet.id;
      me.updateDependentRecordIds(oldValue, value2);
      me.onRecordIdChange({ record, oldValue, value: value2 });
    } else if (!record.meta._ignoreRelatedIdUpdate) {
      me.updateDependentStores("update", [record], silent);
    }
    const committable = (record.ignoreBag || record.isLinked ? false : me.updateModifiedBagForRecord(record)) && // The changes are *not* committable if the record has any pending formulae.
    // The resolved value of formulae will be set in the record when they resolve,
    // and the change event will be triggered again.
    !((_a5 = record.meta) == null ? void 0 : _a5.pendingFormulae);
    me.onUpdateRecord(record, wasSet);
    if (!silent) {
      if ("id" in wasSet) {
        const { oldValue, value: value2 } = toSet.id;
        me.trigger("idChange", {
          store: me,
          record,
          oldValue,
          value: value2
        });
      }
      me.trigger("update", event);
      me.trigger("change", Object.assign({ action: "update" }, event));
    }
    if (me.autoCommit && committable) {
      me.doAutoCommit();
    }
  }
  updateModifiedBagForRecord(record) {
    var _a5;
    const me = this, { modified, added } = me;
    if (record.isModified && !record.isRoot && me.idRegister[record.id]) {
      if (!modified.includes(record) && !added.includes(record)) {
        if (record.isPhantom) {
          added.add(record);
        } else {
          modified.add(record);
        }
      }
      if (record.isBeingMaterialized) {
        me.modifiedMaterialized = (_a5 = me.modifiedMaterialized) != null ? _a5 : /* @__PURE__ */ new Set();
        me.modifiedMaterialized.add(record);
      }
      return true;
    }
    modified.remove(record);
    return false;
  }
  get idMap() {
    const me = this, needsRebuild = !me._idMap, idMap = me._idMap || (me._idMap = {});
    if (needsRebuild) {
      const processedRecords = me.storage.values;
      for (let record, index = 0, visibleIndex = 0; index < processedRecords.length; index++) {
        record = processedRecords[index];
        if (record) {
          idMap[record.id] = { index, visibleIndex, record };
        }
        if (!(record == null ? void 0 : record.isSpecialRow)) {
          visibleIndex++;
        }
      }
      if (me.isFiltered) {
        for (let index = 0, l = me.storage._values.length; index < l; index++) {
          const record = me.storage._values[index];
          if (record) {
            if (record.id in idMap) {
              idMap[record.id].unfilteredIndex = index;
            } else {
              idMap[record.id] = {
                index: -1,
                unfilteredIndex: index,
                record
              };
            }
          }
        }
      }
    }
    return idMap;
  }
  changeModelClass(ClassDef) {
    const { fields } = this;
    this.originalModelClass = ClassDef;
    let ClassDefEx = ClassDef;
    if (fields == null ? void 0 : fields.length) {
      class ModelClass extends ClassDef {
        static get fields() {
          return fields;
        }
      }
      ClassDefEx = ModelClass;
    } else if (!this.preventSubClassingModel) {
      class ModelClass extends ClassDef {
      }
      ClassDefEx = ModelClass;
    }
    ClassDefEx.initClass();
    return ClassDefEx;
  }
  get $lazyLoadPluginClass() {
    return StoreLazyLoadPlugin;
  }
  // Adds the configured plugin when lazyLoad is set to `true`
  changeLazyLoad(lazyLoad) {
    const me = this;
    let lazyLoadClass = me.$lazyLoadPluginClass;
    if (me.isTree && !lazyLoadClass.isOfTypeName("DateStoreLazyLoadPlugin")) {
      if (!me.treeLazyLoadClass) {
        me.treeLazyLoadClass = class DynamicTreeStoreLazyLoadPlugin extends TreeStoreLazyLoadPlugin_default(lazyLoadClass) {
        };
      }
      lazyLoadClass = me.treeLazyLoadClass;
    }
    let plugin = me.getPlugin(lazyLoadClass);
    if (lazyLoad) {
      const isConfig = ObjectHelper.isObject(lazyLoad);
      if (!plugin) {
        plugin = me.addPlugin(lazyLoadClass, isConfig ? lazyLoad : null);
      } else if (isConfig) {
        ObjectHelper.assign(plugin, lazyLoad);
      }
      return plugin;
    } else {
      plugin == null ? void 0 : plugin.destroy();
      return false;
    }
  }
  //endregion
  //region Store id & map
  changeId(id2, oldId) {
    return super.changeId(id2 !== true && id2, oldId);
  }
  updateId(id2, oldId) {
    const duplicate = _Store.getById(id2);
    duplicate && _Store.unregisterInstance(duplicate);
    super.updateId(id2, oldId);
  }
  generateAutoId() {
    return _Store.generateId(`store-`);
  }
  get tree() {
    return this._tree;
  }
  set tree(tree) {
    this._tree = tree;
    if (tree && !this.rootNode) {
      this.rootNode = this.buildRootNode();
      this.rootNode.isAutoRoot = true;
    }
  }
  get autoTree() {
    return this.tree !== false && this._autoTree;
  }
  // a hook to build a customized root node
  buildRootNode() {
    return {};
  }
  /**
   * Get a store from the store map by id.
   * @param {String|Number|Object[]} id The id of the store to retrieve, or an array of objects
   * from which to create the contents of a new Store.
   * @returns {Core.data.Store} The store with the specified id
   */
  static getStore(id2, storeClass) {
    if (id2 instanceof _Store) {
      return id2;
    }
    if (this.getById(id2)) {
      return this.getById(id2);
    }
    if (Array.isArray(id2)) {
      let storeModel;
      const storeData = id2.map((item) => {
        if (item instanceof Model) {
          storeModel = item.constructor;
        } else if (typeof item === "string") {
          item = {
            text: item
          };
        } else {
        }
        return item;
      });
      if (!storeModel) {
        class ModelClass extends Model {
        }
        storeModel = ModelClass;
      }
      id2 = {
        autoCreated: true,
        data: storeData,
        modelClass: storeModel,
        allowNoId: true
        // String items have no id and are not guaranteed to be unique
      };
      if (!storeClass) {
        storeClass = _Store;
      }
    }
    if (storeClass) {
      return new storeClass(id2);
    }
  }
  /**
   * Get all registered stores
   * @property {Core.data.Store[]}
   */
  static get stores() {
    return _Store.registeredInstances;
  }
  //endregion
  //region Data
  /**
   * The invisible root node of this tree.
   * @property {Core.data.Model}
   * @readonly
   * @category Tree
   */
  get rootNode() {
    return !this.isChainedTree && this.masterStore ? this.masterStore.rootNode : this._rootNode;
  }
  set rootNode(rootNode) {
    var _a5;
    const me = this, oldRoot = me._rootNode;
    if (rootNode === oldRoot) {
      return;
    }
    if (oldRoot) {
      me.clear(true);
      me.removed.clear();
    }
    if (rootNode instanceof Model) {
      rootNode.instanceMeta(me).collapsed = false;
      me._rootNode = rootNode;
    } else {
      me._rootNode = rootNode = new me.modelClass({
        expanded: true,
        [me.modelClass.idField]: `${me.id}-rootNode`,
        ...rootNode
      }, me, { isRoot: true }, true);
    }
    me._tree = true;
    rootNode.isRoot = true;
    rootNode.joinStore(me);
    if (((_a5 = rootNode.children) == null ? void 0 : _a5.length) || me.rootVisible) {
      rootNode.isLoading = true;
      me.onNodeAddChild(rootNode, rootNode.children || [], 0);
      rootNode.isLoading = false;
    }
    me.trigger("rootChange", { oldRoot, rootNode });
  }
  /**
   * Sets data in the store.
   *
   * Expects an array of JavaScript objects, with properties matching store's fields (defined on its
   * {@link #config-modelClass model} or in the {@link #config-fields} config).
   *
   * Called on initialization if `data` is in the config, otherwise call it yourself after for example using fetch to
   * get remote data:
   *
   * ```javascript
   * store.data = [
   *     { id : 1, name : 'Linda', city : 'NY' },
   *     { id : 2, name : 'Olivia', city : 'Paris' },
   *     ...
   * ];
   * ```
   *
   * Can also be used to get an array of the current raw data objects from all records in the store (ignoring
   * filtering and grouping):
   *
   * ```javascript
   * console.log(store.data);
   * // [
   * //     { id : 1, name : 'Linda', city : 'NY' },
   * //     { id : 2, name : 'Olivia', city : 'Paris' },
   * //     ...
   * // ]
   * ```
   *
   * {@note}You should not modify the objects in the array, neither the store nor the record will be aware of the
   * changes.{/@note}
   *
   * @property {Object[]}
   * @fires refresh
   * @fires change
   * @category Records
   * @non-lazy-load
   */
  set data(data) {
    this.setStoreData(data);
  }
  get data() {
    return this.getAllDataRecords(true).map((record) => record.data);
  }
  // For overridability in engine
  setStoreData(data) {
    const me = this, { idField, childrenField } = me.modelClass;
    if (data && !Array.isArray(data)) {
      data = ObjectHelper.transformNamedObjectToArray(data, idField);
    }
    const isExtensible = WalkHelper.walkWhile(data != null ? data : [], (n) => n[childrenField], Object.isExtensible);
    if ((data == null ? void 0 : data.length) && !isExtensible && (me.transformFlatData || me.useRawData.enabled)) {
      if (me.transformFlatData) {
        me.useRawData = me.useRawData || {
          disableDuplicateIdCheck: false,
          disableDefaultValue: false,
          disableTypeConversion: false
        };
      }
      data = ObjectHelper.clone(data);
    }
    me.getConfig("plugins");
    me.processConfiguredListeners();
    if (!(me.isChained && !me.isChainedTree)) {
      if (me.autoTree && (data == null ? void 0 : data.some((r) => r[childrenField]))) {
        me.tree = true;
      }
    }
    if (data && (me.remoteSort || me.remoteFilter)) {
      for (let i = 0; i < data.length; i++) {
        data[i]._remoteSortIndex = i;
      }
    }
    if (!me.syncDataOnLoad || !me._data) {
      me._data = data;
      if (me.tree) {
        me.loadTreeData(data);
      } else {
        me.loadData(data);
      }
      me.added.clear();
      me.removed.clear();
      me.modified.clear();
    } else {
      me.syncDataset(data);
    }
  }
  loadTreeData(data) {
    const me = this, root = me.rootNode, expandedState = /* @__PURE__ */ new Map();
    if (me.transformFlatData) {
      data = me.treeifyFlatData(data);
    }
    root.isLoading = true;
    if (me.records.length && me.keepExpandedOnLoad) {
      me.traverse((record) => {
        if (!record.isLeaf) {
          expandedState.set(record.id, record.instanceMeta(me).collapsed);
        }
      });
    }
    data.sort((a, b) => a.parentIndex - b.parentIndex);
    me.clear(true);
    me.suspendEvents(true);
    root.appendChild(data);
    expandedState.forEach((isCollapsed, recordId) => {
      const record = me.getById(recordId);
      if (record) {
        me.toggleCollapse(record, isCollapsed);
      }
    });
    me.resumeEvents();
    me.updateDependentStores("dataset", [root]);
    root.isLoading = false;
    if (data.length === 0) {
      const event = { action: "dataset", data: [], records: [] };
      me.trigger("refresh", event);
      me.trigger("change", event);
    } else if (me.isFiltered && !me.remoteFilter) {
      me.filter();
    }
  }
  loadData(data, action = "dataset") {
    var _a5;
    const me = this, { storage, allowNoId } = me, idField = me.modelClass.fieldMap.id.dataSource, creatingRecord = me.find((rec) => rec.isCreating);
    if (creatingRecord) {
      storage.values.splice(me.records.indexOf(creatingRecord), 1);
    }
    let warnGenerated = me.verifyNoGeneratedIds;
    me.removeHeadersAndFooters(me.storage.values);
    me._idMap = null;
    me.oldIdMap = {};
    if (data) {
      const isRaw = !(data[0] instanceof Model), count = data.length, records = new Array(count);
      if (isRaw) {
        me.modelClass.exposeProperties(data[0]);
      }
      for (let i = 0; i < count; i++) {
        let record = data[i];
        if (isRaw) {
          const id2 = record[idField];
          if (!allowNoId && id2 == null) {
            throw new Error(`Id required but not found on row ${i}`);
          }
          if (warnGenerated && ((_a5 = id2 == null ? void 0 : id2.startsWith) == null ? void 0 : _a5.call(id2, "_generated"))) {
            console.warn(`Generated id found in data: ${id2}. Generated ids are temporary and should be replaced with real ids by the backend`);
            warnGenerated = false;
          }
          record = me.createRecord(record, true);
        }
        if (!me.isFillingFromMaster) {
          record = me.processRecord(record, true);
          record.setData("parentIndex", i);
        }
        records[i] = record;
      }
      me.clear(true);
      storage.replaceValues({
        values: records,
        isNewDataset: true,
        silent: true
      });
      if (creatingRecord && !storage.values.includes(creatingRecord)) {
        storage.values.push(creatingRecord);
      }
      me._data = data;
      me.onDataReplaced(action, data);
    } else {
      me.clear(true);
      me._data = null;
    }
    me.isSyncingDataOnLoad = false;
  }
  /**
   * Creates an array of records from this store from the `start` to the `end` - 1
   * @param {Number} [start] The index of the first record to return
   * @param {Number} [end] The index *after* the last record to return `(start + length)`
   * @returns {Core.data.Model[]} The requested records.
   * @category Records
   * @non-lazy-load
   */
  getRange(start, end, all = true) {
    return (all ? this.storage.allValues : this.storage.values).slice(start, end);
  }
  /**
   * Creates a model instance, used internally when data is set/added.
   * Provide this method for your own custom conversion from data to record.
   * @config {Function} createRecord
   * @param {*} data Json data
   * @param {Boolean} [skipExpose=false] Supply true when batch setting to not expose properties multiple times
   * @returns {Core.data.Model}
   * @category Records
   */
  /**
   * Creates a model instance, used internally when data is set/added. Override this in a subclass to do your own custom
   * conversion from data to record.
   * @param {Object} data Json data
   * @param {Boolean} [skipExpose=false] Supply true when batch setting to not expose properties multiple times
   * @returns {Core.data.Model}
   * @category Records
   */
  createRecord(data, skipExpose = false, rawData = false) {
    return new this._modelClass(data, this, null, skipExpose, false, rawData);
  }
  processRecord(record, isDataset = false) {
    return record;
  }
  refreshData() {
    this.filter();
    this.sort();
  }
  onRecordIdChange({ record, oldValue, value: value2 }) {
    const me = this, idMap = me._idMap, { idRegister, oldIdMap } = me;
    me.storage._indicesInvalid = true;
    oldIdMap[oldValue] = record;
    if (idMap && !idMap[value2]) {
      const entry = idMap[oldValue];
      delete idMap[oldValue];
      idMap[value2] = entry;
    }
    me.added.changeId(oldValue, value2);
    me.removed.changeId(oldValue, value2);
    me.modified.changeId(oldValue, value2);
    delete idRegister[oldValue];
    idRegister[value2] = record;
    record.index = me.storage.indexOf(record);
  }
  onUpdateRecord(record, changes) {
    var _a5, _b2;
    const me = this, { internalId } = changes, { internalIdRegister, reapplyFilterOnUpdate, reapplySortersOnUpdate } = me;
    if (internalId) {
      this.storage._indicesInvalid = true;
      delete internalIdRegister[internalId.oldValue];
      internalIdRegister[internalId.value] = record;
    }
    if ((reapplyFilterOnUpdate === true || ((_a5 = reapplyFilterOnUpdate.fields) == null ? void 0 : _a5.some((field) => field in changes))) && me.isFiltered) {
      if (!me.filterBatching) {
        me.filter();
      }
    }
    if ((reapplySortersOnUpdate === true || ((_b2 = reapplySortersOnUpdate.fields) == null ? void 0 : _b2.some((field) => field in changes))) && me.isSorted) {
      if (!me._reapplySortersSuspended) {
        me.sort();
      } else {
        me._shouldReapplySorters = true;
      }
    }
  }
  get useRawData() {
    var _a5;
    if (this._useRawData === useRawDataUnset) {
      if (this.readUrl || ((_a5 = this.crudManager) == null ? void 0 : _a5.loadUrl)) {
        return useRawDataRemoteDefaults;
      }
      return { enabled: false };
    }
    return this._useRawData;
  }
  changeUseRawData(options) {
    if (options === useRawDataUnset) {
      return useRawDataUnset;
    }
    if (options === true) {
      return useRawDataDefaults;
    }
    return options ? Object.assign(options, { enabled: true }) : { enabled: false };
  }
  /**
   * This event only fires in a non-{@link Core.data.AjaxStore}, configured with {@link #config-remoteSort},
   * {@link #config-remoteFilter} or {@link #config-remotePaging}, when the Store requests more or new data.
   *
   * The event will contain same params as described in the {@link #config-requestData} function. This event can
   * be listened to if you want to receive notifications about the Store's data requests, but not *directly* want to
   * return the requested data. For example, when you got the Store's {@link #config-data} bound to a data source with
   * the help of an external library/framegrunt docswork.
   *
   * @event requestData
   * @property {Number} startIndex The index of the first record being requested (only lazyLoad)
   * @property {Number} count The number of records being requested (only lazyLoad)
   * @property {Number} page The page number being requested (only remotePaging)
   * @property {Number} pageSize The number of records being requested (only remotePaging)
   * @property {Sorter[]} sorters If {@link #config-remoteSort} is active, this will contain a number of sorter objects
   * @property {CollectionFilterConfig[]} filters If {@link #config-remoteFilter} is active, this will contain a number
   */
  async performDataRequest(silent, extraParams, setDataHook) {
    var _a5, _b2;
    const me = this, params = me.buildRemoteParams(extraParams);
    let data, total;
    if (!silent && (await me.trigger("beforeRequest", { action: "read", params }) === false || // To match AjaxStore
    await ((_a5 = me.trigger) == null ? void 0 : _a5.call(me, "requestData", params)) === false)) {
      return false;
    }
    if (me.isDestroyed) {
      return;
    }
    if (me.requestData) {
      ({ data, total } = await me.requestData(params));
      if (me.isDestroyed) {
        return;
      }
      setDataHook == null ? void 0 : setDataHook();
      me.data = data;
      if (total != null) {
        me.remoteTotal = total;
      }
    } else {
      setDataHook == null ? void 0 : setDataHook();
    }
    await ((_b2 = me.trigger) == null ? void 0 : _b2.call(me, "afterRequest", { response: { data, total }, params, action: "read" }));
  }
  //endregion
  //region Count
  /**
   * Counts the number of records in the store. Allows passing an options object to control which records are counted.
   * For example:
   *
   * ```javascript
   * store.getCount({ filteredOut : true });
   * store.getCount({ headersFooters : true, collapsed : true });
   * ```
   *
   * Consider the following dataset loaded into a Store:
   *
   * ```javascript
   * [
   *   { id : 1, name : 'Linda', city : 'NY' },
   *   { id : 2, name : 'Olivia', city : 'NY' },
   *   { id : 3, name : 'John', city : 'Stockholm' }
   * ]
   * ```
   *
   * No grouping or filtering is applied, thus all records are accessible ("visible"). Passing the different options
   * below would yield the following results:
   *
   * | Option           | Count | Description                          |
   * |------------------|-------|--------------------------------------|
   * | `filteredOut`    | 0     | No records are filtered out          |
   * | `headersFooters` | 0     | No group headers have been generated |
   * | `collapsed`      | 0     | No groups are collapsed              |
   * | `visibleData`    | 3     | All data records are "visible"       |
   * | `all`            | 3     | All options combined                 |
   *
   * Grouping by `city` yields the following records in the Store (pseudocode):
   *
   * ```javascript
   * store.group('city');
   *
   * [
   *   { name : 'Group NY', groupHeader : true, expanded : true },
   *   { id : 1, name : 'Linda', city : 'NY' },
   *   { id : 2, name : 'Olivia', city : 'NY' },
   *   { name : 'Group Stockholm', groupHeader : true },
   *   { id : 3, name : 'John', city : 'Stockholm', expanded : true }
   * ]
   * ```
   *
   * Passing the different options below would yield the following results:
   *
   * | Option           | Count | Description                          |
   * |------------------|-------|--------------------------------------|
   * | `filteredOut`    | 0     | No records are filtered out          |
   * | `headersFooters` | 2     | Group headers have been generated    |
   * | `collapsed`      | 0     | No groups are collapsed              |
   * | `visibleData`    | 3     | All data records are "visible"       |
   * | `all`            | 5     | All options combined                 |
   *
   * Collapsing the `Stockholm` group yields the following records in the Store (pseudocode):
   *
   * ```javascript
   * store.collapse(store.getAt(3));
   *
   * [
   *   { name : 'Group NY', groupHeader : true, expanded : true },
   *   { id : 1, name : 'Linda', city : 'NY' },
   *   { id : 2, name : 'Olivia', city : 'NY' },
   *   { name : 'Group Stockholm', groupHeader : true, expanded : false }
   * ]
   * ```
   *
   * Passing the different options below would yield the following results:
   *
   * | Option           | Count | Description                          |
   * |------------------|-------|--------------------------------------|
   * | `filteredOut`    | 0     | No records are filtered out          |
   * | `headersFooters` | 2     | Group headers have been generated    |
   * | `collapsed`      | 1     | A group with one record is collapsed |
   * | `visibleData`    | 2     | Not all data records are "visible"   |
   * | `all`            | 5     | All options combined                 |
   *
   * Applying a filter further affects the counts (pseudocode):
   *
   * ```javascript
   * store.filter('name', 'Linda');
   *
   * [
   *   { name : 'Group NY', groupHeader : true, expanded : true },
   *   { id : 1, name : 'Linda', city : 'NY' }
   * ]
   * ```
   *
   * Passing the different options below would yield the following results:
   *
   * | Option           | Count | Description                          |
   * |------------------|-------|--------------------------------------|
   * | `filteredOut`    | 2     | Records are filtered out             |
   * | `headersFooters` | 2     | Group headers have been generated*   |
   * | `collapsed`      | 1     | A group with one record is collapsed |
   * | `visibleData`    | 1     | Not all data records are "visible"   |
   * | `all`            | 5     | All options combined                 |
   *
   * <sup>*</sup> Note that also passing `filteredOut` would include filtered out group headers in the count.
   *
   * @param {Boolean|Object} [options] Count processed (true) or real records (false), or pass an object with
   * options to control the count. Note that the Boolean signature is deprecated and will be removed in a future
   * version.
   * @param {Boolean} [options.filteredOut=false] Count records that are filtered out
   * @param {Boolean} [options.headersFooters=false] Count generated group headers and footers (not applicable for
   * tree data)
   * @param {Boolean} [options.collapsed=false] Count records that are collapsed away, either belonging to a
   * collapsed group, or by belonging to a collapsed branch in a tree
   * @param {Boolean} [options.visibleData=true] Count data records that are not filtered out or collapsed away
   * @param {Boolean} [options.all=false] Convenience option to count all records (all options as `true`)
   * @returns {Number} Record count
   * @category Records
   */
  getCount(options = true) {
    var _a5, _b2, _c2;
    const me = this, {
      storage,
      groupRecords,
      isFiltered,
      isGrouped,
      isTree,
      rootNode
    } = me;
    if (typeof options === "boolean") {
      VersionHelper.deprecate("core", "7.0.0", "getCount(true/false) deprecated in favor of getCount(options)");
      return options ? me.count : storage.totalCount - ((groupRecords == null ? void 0 : groupRecords.count) || 0);
    }
    if (options.all) {
      return this.allRecords.length;
    }
    if (!("visibleData" in options)) {
      options.visibleData = true;
    }
    let count = 0;
    const {
      visibleData,
      headersFooters,
      filteredOut,
      collapsed
    } = options, generatedCount = isFiltered ? (_a5 = groupRecords == null ? void 0 : groupRecords.values.reduce((count2, group) => count2 + (me.includes(group) ? 1 : 0), 0)) != null ? _a5 : 0 : (_b2 = groupRecords == null ? void 0 : groupRecords.count) != null ? _b2 : 0, accessibleCount = storage.count - generatedCount;
    if (visibleData) {
      count += accessibleCount;
    }
    if (headersFooters && isGrouped) {
      if (isFiltered && filteredOut) {
        count += (_c2 = groupRecords.count) != null ? _c2 : 0;
      } else {
        count += generatedCount;
      }
    }
    if (filteredOut && isFiltered) {
      if (isTree) {
        count += rootNode.getDescendantCount(false, me, true) - rootNode.getDescendantCount(false, me, false);
      } else {
        count += storage.allValues.length - storage.values.length;
      }
    }
    if (collapsed) {
      if (isGrouped) {
        let collapsedCount = 0;
        for (const grp of me.collapsedGroups) {
          collapsedCount += groupRecords.get(grp).groupChildren.length;
        }
        count += collapsedCount;
      } else if (isTree) {
        count += rootNode.descendantCount - accessibleCount;
      }
    }
    return count;
  }
  /**
   * Record count, including records added for group headers etc., excluding collapsed or filtered away records. That
   * is, `count` is the "visible record count", if the store is used in a Grid or other component that visualize the
   * records.
   *
   * Consider the following dataset loaded into a Store:
   *
   * ```javascript
   * [
   *   { id : 1, name : 'Linda', city : 'NY' },
   *   { id : 2, name : 'Olivia', city : 'NY' },
   *   { id : 3, name : 'John', city : 'Stockholm' }
   * ]
   *
   * // count === 3
   * ```
   *
   * Grouping by `city` yields the following records in the Store (pseudocode):
   *
   * ```javascript
   * store.group('city');
   *
   * [
   *   { name : 'Group NY', groupHeader : true, expanded : true },
   *   { id : 1, name : 'Linda', city : 'NY' },
   *   { id : 2, name : 'Olivia', city : 'NY' },
   *   { name : 'Group Stockholm', groupHeader : true },
   *   { id : 3, name : 'John', city : 'Stockholm', expanded : true }
   * ]
   *
   * // count === 5
   * ```
   *
   * Collapsing the `Stockholm` group yields the following records in the Store (pseudocode):
   *
   * ```javascript
   * store.collapse(store.getAt(3));
   *
   * [
   *   { name : 'Group NY', groupHeader : true, expanded : true },
   *   { id : 1, name : 'Linda', city : 'NY' },
   *   { id : 2, name : 'Olivia', city : 'NY' },
   *   { name : 'Group Stockholm', groupHeader : true, expanded : false }
   * ]
   *
   * // count === 4
   * ```
   *
   * Applying a filter further affects the count (pseudocode):
   *
   * ```javascript
   * store.filter('name', 'Linda');
   *
   * [
   *   { name : 'Group NY', groupHeader : true, expanded : true },
   *   { id : 1, name : 'Linda', city : 'NY' }
   * ]
   *
   * // count === 2
   * ```
   *
   * If you need a different value for `count` in your app, for example ignoring filters, or including collapsed away
   * records, please use the more flexible {@link #function-getCount} function instead.
   *
   * If the store is configured with {@link Core.data.Store#config-lazyLoad}, this number is based on the total amount
   * of records specified in the response given to the {@link Core.data.Store#config-requestData} function.
   *
   * @property {Number}
   * @readonly
   * @category Records
   */
  get count() {
    return this.storage.count;
  }
  /**
   * Record count, for data records. Not including records added for group headers etc., affected by collapsing
   * groups.
   *
   * {@note}This property has been deprecated, please use the more flexible {@link #function-getCount} function
   * instead to have greater control over what is counted.{/@note}
   *
   * @property {Number}
   * @readonly
   * @category Records
   * @deprecated 6.0.2 Use {@link #function-getCount} instead
   */
  get originalCount() {
    var _a5;
    VersionHelper.deprecate("core", "7.0.0", "originalCount was deprecated in favor of getCount()");
    return this.storage.totalCount - (((_a5 = this.groupRecords) == null ? void 0 : _a5.count) || 0);
  }
  /**
   * Returns the total number of records in this store. Note:
   *
   * - In a tree store: Includes all nodes regardless of tree node collapsing or filtering.
   *
   * - In a grouped store: Filtered out records are included, records in collapsed groups are not.
   *
   * - In a paged store: This returns the value returned in the last loaded data block in the
   *   {@link Core.data.AjaxStore#config-responseTotalProperty}
   *
   * {@note}This property has been deprecated, please use the more flexible {@link #function-getCount} function
   * instead to have greater control over what is counted.{/@note}
   *
   * @property {Number}
   * @readonly
   * @category Records
   * @deprecated 6.0.2 Use {@link #function-getCount} or {@link #property-totalCount} instead
   */
  get allCount() {
    VersionHelper.deprecate("core", "7.0.0", "allCount was deprecated in favor of totalCount");
    return this.isTree ? this.rootNode.descendantCount : this.storage.totalCount;
  }
  /**
   * Yields the complete dataset size, ignoring filtering and grouping. If the store
   * {@link #property-isPaged is paged}, it returns the `total` value provided in the page load request response, or
   * manually set.
   * @property {Number}
   * @category Records
   */
  get totalCount() {
    if (this.isPaged && "remoteTotal" in this) {
      return this.remoteTotal;
    }
    return this.getCount({ all: true });
  }
  set totalCount(count) {
    if (this.isPaged) {
      this.remoteTotal = count;
    }
  }
  //endregion
  //region Get record(s)
  /**
   * Returns all locally available "visible" records.
   * **Note:** The returned value **may not** be mutated!
   * @property {Core.data.Model[]}
   * @readonly
   * @immutable
   * @category Records
   */
  get records() {
    return this.storage.values;
  }
  /**
   * Get the first record locally available in the store.
   * @property {Core.data.Model}
   * @readonly
   * @category Records
   */
  get first() {
    return this.storage.values[0];
  }
  /**
   * Get the last record locally available in the store.
   * @property {Core.data.Model}
   * @readonly
   * @category Records
   */
  get last() {
    return this.storage.values[this.storage.values.length - 1];
  }
  /**
   * Get the record at the specified index.
   * @param {Number} index Index for the record
   * @returns {Core.data.Model} Record at the specified index
   * @category Records
   */
  getAt(index, all = false) {
    return this.storage.getAt(index, all);
  }
  // These are called by Model#join and Model#unjoin
  // register a record as a findable member keyed by id and internalId
  register(record) {
    const me = this;
    if (!me.useRawData.disableDuplicateIdCheck) {
      const existingRec = me.isTree && me.idRegister[record.id];
      if (existingRec && existingRec !== record) {
        throw new Error(`Id collision on ${record.id}`);
      }
    }
    me.idRegister[record.id] = record;
    me.internalIdRegister[record.internalId] = record;
  }
  unregister(record) {
    delete this.idRegister[record.id];
    delete this.internalIdRegister[record.internalId];
  }
  get registeredRecords() {
    return Object.values(this.idRegister);
  }
  /**
   * Get a locally available record by id. Find the record even if filtered out, part of collapsed group or collapsed
   * node
   * @param {Core.data.Model|String|Number} id Id of record to return.
   * @returns {Core.data.Model} A record with the specified id
   * @category Records
   */
  getById(id2) {
    const me = this;
    let record;
    if (id2 == null ? void 0 : id2.isModel) {
      record = id2;
      id2 = record.id;
    }
    const found = me.idRegister[id2];
    if (record) {
      if ((record == null ? void 0 : record.hasLinks) && !found) {
        const { allValues } = me.storage;
        return record.$links.find((r) => allValues.includes(r));
      }
      return record;
    }
    return found;
  }
  /**
   * Checks if a record is available, in the sense that it is not filtered out,
   * hidden in a collapsed group or in a collapsed parent node of a tree store.
   * @param {Core.data.Model|String|Number} recordOrId Record to check
   * @returns {Boolean}
   * @category Records
   */
  isAvailable(recordOrId) {
    const record = this.getById(recordOrId);
    return record && this.storage.includes(record) || false;
  }
  /**
   * Get a record by internalId.
   * @param {Number} internalId The internalId of the record to return
   * @returns {Core.data.Model} A record with the specified internalId
   * @category Records
   */
  getByInternalId(internalId) {
    return this.internalIdRegister[internalId];
  }
  /**
   * Checks if the specified record is contained in the store
   * @param {Core.data.Model|String|Number} recordOrId Record, or `id` of record
   * @returns {Boolean}
   * @category Records
   */
  includes(recordOrId) {
    if (this.isTree) {
      return this.idRegister[Model.asId(recordOrId)] != null;
    }
    return this.indexOf(recordOrId) > -1;
  }
  //endregion
  //region Get index
  /**
   * Returns the index of the specified record/id, or `-1` if not found.
   * @param {Core.data.Model|String|Number} recordOrId Record, or `id` of record to return the index of.
   * @param {Boolean} [visibleRecords] Pass `true` to find the visible index.
   * as opposed to the dataset index. This omits group header records.
   * @param {Boolean} [allExceptFilteredOutRecords] For trees, when true this searches all except filtered out records
   * in the flattened tree, similar to a flat store.
   * @returns {Number} Index for the record/id, or `-1` if not found.
   * @category Records
   */
  indexOf(recordOrId, visibleRecords = false, allExceptFilteredOutRecords = false) {
    var _a5;
    if ((recordOrId == null ? void 0 : recordOrId.isModel) && !((_a5 = recordOrId.stores) == null ? void 0 : _a5.includes(this.$store || this))) {
      const linkedRecord = recordOrId.$links.find((r) => this.storage.allValues.includes(r));
      if (linkedRecord) {
        return this.indexOf(linkedRecord, visibleRecords);
      }
      return -1;
    }
    if (this.isTree) {
      return (allExceptFilteredOutRecords ? this.rootNode.allChildren : this.storage.values).indexOf(this.getById(recordOrId));
    }
    const id2 = Model.asId(recordOrId);
    if (id2 == null) {
      return -1;
    }
    const found = this.idMap[id2];
    return found ? found[visibleRecords ? "visibleIndex" : "index"] : -1;
  }
  allIndexOf(recordOrId) {
    if (this.isTree) {
      return this.allRecords.indexOf(this.getById(recordOrId));
    } else {
      return this.storage.indexOf(recordOrId, true);
    }
  }
  //endregion
  //region Get values
  /**
   * Returns an array of distinct values from all locally available records for the specified field.
   *
   * ```javascript
   * store.getDistinctValues('age'); // Returns an array of the unique age values
   * ```
   *
   * @param {String} field Field to extract values for
   * @param {Boolean} [includeFilteredOutRecords] True to ignore any applied filters
   * @param {Boolean} [includeCounts] When `true`, returns array of `[ value, count ]` with counts of occurrences
   * @returns {Array} Array of values
   * @category Values
   */
  getDistinctValues(field, includeFilteredOutRecords = false, includeCounts = false) {
    const me = this, values = [], counts = {};
    let value2;
    me.forEach((r) => {
      if (!r.isSpecialRow && !r.isRoot) {
        value2 = r.getValue(field);
        const primitiveValue = value2 instanceof Date ? value2.getTime() : value2;
        if (counts[primitiveValue] === void 0) {
          values.push(value2);
          counts[primitiveValue] = { value: value2, count: 1 };
        } else {
          counts[primitiveValue].count++;
        }
      }
    }, me, { includeCollapsedGroupRecords: true, includeFilteredOutRecords });
    return includeCounts ? Object.values(counts) : values;
  }
  /**
   * Counts how many times the specified value appears locally in the store
   * @param {String} field Field to look in
   * @param {*} value Value to look for
   * @returns {Number} Found count
   * @category Values
   */
  getValueCount(field, value2) {
    let count = 0;
    this.forEach((r) => {
      if (ObjectHelper.isEqual(r.getValue(field), value2))
        count++;
    });
    return count;
  }
  //endregion
  //region JSON & console
  /**
   * Retrieve or set the data of all records as a JSON string
   *
   * ```javascript
   * const store = new Store({
   *     data : [
   *         { id : 1, name : 'Superman' },
   *         { id : 2, name : 'Batman' }
   *     ]
   * });
   *
   * const jsonString = store.json;
   *
   * //jsonString:
   * '[{"id":1,"name":"Superman"},{"id":2,"name":"Batman"}]
   * ```
   *
   * @property {String}
   * @non-lazy-load
   */
  set json(json) {
    if (typeof json === "string") {
      json = StringHelper.safeJsonParse(json);
    }
    this.data = json;
  }
  get json() {
    return StringHelper.safeJsonStringify(this);
  }
  /**
   * Pretty printed version of {@link #property-json}
   * @readonly
   * @property {String}
   */
  get formattedJSON() {
    return StringHelper.safeJsonStringify(this, null, 4);
  }
  /**
   * Retrieve the data of all (unfiltered) records as an array of JSON objects.
   *
   * ```javascript
   * const store = new Store({
   *     data : [
   *         { id : 1, name : 'Superman' },
   *         { id : 2, name : 'Batman' }
   *     ]
   * });
   *
   * const jsonArray = store.toJSON();
   *
   * //jsonArray:
   * [{id:1,name:"Superman"},{id:2,name:"Batman"}]
   * ```
   *
   * @returns {Object[]}
   */
  toJSON() {
    const me = this, { persistable, dataSource } = me.modelClass.getFieldDefinition("expanded");
    return (me.isTree ? me.rootNode.unfilteredChildren || me.rootNode.children || [] : me.allRecords).map((record) => {
      const json = record.toJSON();
      if (me.tree && record.isParent && persistable) {
        json[dataSource] = record.isExpanded(me);
      }
      return json;
    });
  }
  //endregion
  //region Extract config
  // These functions are not meant to be called by any code other than Base#getCurrentConfig()
  preProcessCurrentConfigs(configs) {
    super.preProcessCurrentConfigs(configs);
    delete configs.project;
  }
  // Extract current data for all accessible records
  getInlineData(options) {
    var _a5;
    const data = [];
    if (this.tree) {
      (_a5 = this.rootNode.children) == null ? void 0 : _a5.forEach((r) => data.push(r.getCurrentConfig(options)));
    } else {
      this.forEach((r) => data.push(r.getCurrentConfig(options)));
    }
    return data;
  }
  // Extract current configs and data
  getCurrentConfig(options) {
    var _a5;
    const result = super.getCurrentConfig(options), { state } = this;
    if (result) {
      if (result.data) {
        result.data = this.getInlineData(options);
      }
      delete result.project;
      delete result.stm;
      delete result.asyncEvents;
      if (((_a5 = result.modelClass) == null ? void 0 : _a5.$meta.hierarchy[result.modelClass.$meta.hierarchy.length - 2]) === this.constructor.defaultConfig.modelClass) {
        delete result.modelClass;
      }
      if (!this.tree) {
        delete result.tree;
      }
      if (state) {
        Object.assign(result, state);
      }
    }
    return result;
  }
  //endregion
  //region Iteration & traversing
  /**
   * Iterates over all available records in store. Omits group header and footer records if this store is grouped.
   * Does *not* request new records when store is configured with
   * {@link Core.data.Store#config-lazyLoad}.
   * @param {Function} fn A function that is called for each record. Returning `false` from that function cancels
   * iteration. It is called with the following arguments:
   * @param {Core.data.Model} fn.record Current record
   * @param {Number} fn.index Current index
   * @param {Object} [thisObj] `this` reference for the function
   * @param {Object|Boolean} [options] A boolean for `includeFilteredOutRecords`, or detailed options for
   * exclude/include records
   * @param {Boolean} [options.includeFilteredOutRecords] `true` to also include filtered out records
   * @param {Boolean} [options.includeCollapsedGroupRecords] `true` to also include records from collapsed groups of
   * grouped stores
   * @category Iteration
   */
  forEach(fn2, thisObj = this, options) {
    const me = this, callback = (r, i) => {
      if (r && !r.isRoot && !r.isSpecialRow) {
        return fn2.call(thisObj, r, i);
      }
    };
    options = fixTraverseOptions2(me, options);
    if (me.isTree) {
      if (me.isChained) {
        options = {
          ...options,
          sorterFn: me.sorterFn
        };
      }
      me.rootNode.traverseWhile(callback, false, options);
    } else {
      const records = options.includeFilteredOutRecords ? me.storage.allValues : me.storage.values;
      if (me.isGrouped && options.includeCollapsedGroupRecords) {
        for (let i = 0; i < records.length; i++) {
          const record = records[i], groupChildren = options.includeFilteredOutRecords ? record.unfilteredGroupChildren : record.groupChildren;
          if (groupChildren && record.meta.collapsed === true) {
            for (let j = 0; j < groupChildren.length; j++) {
              const rec = groupChildren[j];
              if (callback(rec, j) === false) {
                return;
              }
            }
          } else if (callback(record, i) === false) {
            return;
          }
        }
      } else {
        for (let i = 0; i < records.length; i++) {
          if (callback(records[i], i) === false) {
            return;
          }
        }
      }
    }
  }
  /**
   * Equivalent to Array.map(). Creates a new array with the results of calling a provided function on every record
   * @param {Function} fn
   * @param {Object} [thisObj] The `this` reference to call the function with. Defaults to this Store
   * @returns {Array}
   * @category Iteration
   */
  map(fn2, thisObj = this) {
    return this.storage.values.map(fn2, thisObj);
  }
  /**
   * Equivalent to {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/flatMap Array.flatMap()}.
   * Creates a new array by spreading the results of calling a provided function on every record
   * @param {Function} fn A function returning an array of items to be spread into the new array, or a single item to include in it
   * @param {Object} [thisObj] The `this` reference to call the function with. Defaults to this Store
   * @returns {Array} The new array
   * @category Iteration
   */
  flatMap(fn2, thisObj = this) {
    return this.storage.values.flatMap(fn2, thisObj);
  }
  /**
   * Equivalent to Array.every(). Returns `true` if every call of the provided function
   * on each record yields a truthy value.
   * @param {Function} fn
   * @param {Function} fn.record The record to test.
   * @param {Object} [thisObj] The `this` reference to call the function with. Defaults to this Store.
   * @param {Boolean} [ignoreFilters] Pass `true` to iterate all records including filtered out ones.
   * @returns {Array}
   * @category Iteration
   */
  every(fn2, thisObj = this, ignoreFilters) {
    return this.storage[ignoreFilters ? "allValues" : "values"].every(fn2, thisObj);
  }
  /**
   * Equivalent to Array.reduce(). Applies a function against an accumulator and each record (from left to right) to
   * reduce it to a single value.
   * @param {Function} fn
   * @param {*} initialValue
   * @param {Object} [thisObj] The `this` reference to call the function with. Defaults to this Store
   * @returns {*}
   * @category Iteration
   */
  reduce(fn2, initialValue = [], thisObj = this) {
    if (thisObj !== this) {
      fn2 = fn2.bind(thisObj);
    }
    return this.storage.values.reduce(fn2, initialValue, thisObj);
  }
  /**
   * Iterator that allows you to do for (let record of store)
   * @category Iteration
   */
  [Symbol.iterator]() {
    return this.storage.values[Symbol.iterator]();
  }
  /**
   * Traverse all tree nodes (only applicable for Tree Store)
   * @param {Function} fn The function to call on visiting each node.
   * @param {Core.data.Model} [topNode=this.rootNode] The top node to start the traverse at.
   * @param {Boolean} [skipTopNode] Pass true to not call `fn` on the top node, but proceed directly to its children.
   * @param {Object|Boolean} [options] A boolean for includeFilteredOutRecords, or detailed options for exclude/include records
   * @param {Boolean} [options.includeFilteredOutRecords] True to also include filtered out records
   * @param {Boolean} [options.includeCollapsedGroupRecords] True to also include records from collapsed groups of grouped store
   * @param {Boolean} [options.useOrderedTree] True to traverse unsorted/unfiltered tree
   * @category Traverse
   * @non-lazy-load
   */
  traverse(fn2, topNode = this.rootNode, skipTopNode = topNode === this.rootNode, options) {
    const me = this;
    options = fixTraverseOptions2(me, options);
    if (me.isTree) {
      if (typeof topNode === "boolean") {
        skipTopNode = topNode;
        topNode = me.rootNode;
      }
      if (me.isChained) {
        const passedFn = fn2;
        fn2 = (node) => {
          if (me.chainedFilterFn(node)) {
            passedFn(node);
          }
        };
      }
      topNode.traverse(fn2, skipTopNode, options);
    } else {
      me.forEach((rec) => rec.traverse(fn2, false, options), me, options);
    }
  }
  /**
   * Traverse all tree nodes while the passed `fn` returns true
   * @param {Function} fn The function to call on visiting each node. Returning `false` from it stops the traverse.
   * @param {Core.data.Model} [topNode=this.rootNode] The top node to start the traverse at.
   * @param {Boolean} [skipTopNode] Pass true to not call `fn` on the top node, but proceed directly to its children.
   * @param {Object} [options] An options object to exclude/include records
   * @param {Boolean} [options.includeFilteredOutRecords] True to also include filtered out records
   * @param {Boolean} [options.includeCollapsedGroupRecords] True to also include records from collapsed groups of grouped store
   * @category Traverse
   * @non-lazy-load
   */
  traverseWhile(fn2, topNode = this.rootNode, skipTopNode = topNode === this.rootNode, options) {
    const me = this;
    options = fixTraverseOptions2(me, options);
    if (me.isTree) {
      if (typeof topNode === "boolean") {
        skipTopNode = topNode;
        topNode = me.rootNode;
      }
      if (me.isChained) {
        const passedFn = fn2;
        fn2 = (node) => {
          if (me.chainedFilterFn(node)) {
            passedFn(node);
          }
        };
      }
      topNode.traverseWhile(fn2, skipTopNode, options);
    } else {
      for (const record of me.storage) {
        if (record.traverse(fn2, false, options) === false) {
          break;
        }
      }
    }
  }
  /**
   * Finds the next record locally available.
   * @param {Core.data.Model|String|Number} recordOrId Current record or its id
   * @param {Boolean} [wrap=false] Wrap at start/end or stop there
   * @param {Boolean} [skipSpecialRows=false] True to not return specialRows like group headers
   * @returns {Core.data.Model} Next record or null if current is the last one
   * @category Traverse
   */
  getNext(recordOrId, wrap = false, skipSpecialRows = false) {
    const me = this, records = me.storage.values;
    let idx = me.indexOf(recordOrId);
    if (idx >= records.length - 1) {
      if (wrap) {
        idx = -1;
      } else {
        return null;
      }
    }
    const record = records[idx + 1];
    if (skipSpecialRows && record && (record.isSpecialRow || typeof skipSpecialRows === "function" && skipSpecialRows(record))) {
      return me.getNext(records[idx + 1], wrap, skipSpecialRows);
    }
    return record;
  }
  /**
   * Finds the previous record locally available.
   * @param {Core.data.Model|String|Number} recordOrId Current record or its id
   * @param {Boolean} [wrap=false] Wrap at start/end or stop there
   * @param {Boolean} [skipSpecialRows=false] True to not return specialRows like group headers
   * @returns {Core.data.Model} Previous record or null if current is the last one
   * @category Traverse
   */
  getPrev(recordOrId, wrap = false, skipSpecialRows = false) {
    const me = this, records = me.storage.values;
    let idx = me.indexOf(recordOrId);
    if (idx === 0) {
      if (wrap) {
        idx = records.length;
      } else {
        return null;
      }
    }
    const record = records[idx - 1];
    if (idx > 0 && skipSpecialRows && record && (record.isSpecialRow || typeof skipSpecialRows === "function" && skipSpecialRows(record))) {
      return me.getPrev(records[idx - 1], wrap, skipSpecialRows);
    }
    return record;
  }
  /**
   * Gets the next or the previous record locally available. Optionally wraps from first -> last and vice versa
   * @param {String|Core.data.Model} recordOrId Record or records id
   * @param {Boolean} next Next (true) or previous (false)
   * @param {Boolean} wrap Wrap at start/end or stop there
   * @param {Boolean} [skipSpecialRows=false] True to not return specialRows like group headers
   * @returns {Core.data.Model}
   * @category Traverse
   * @internal
   */
  getAdjacent(recordOrId, next = true, wrap = false, skipSpecialRows = false) {
    return next ? this.getNext(recordOrId, wrap, skipSpecialRows) : this.getPrev(recordOrId, wrap, skipSpecialRows);
  }
  /**
   * Finds the next record among leaves (in a tree structure)
   * @param {Core.data.Model|String|Number} recordOrId Current record or its id
   * @param {Boolean} [wrap] Wrap at start/end or stop there
   * @returns {Core.data.Model} Next record or null if current is the last one
   * @category Traverse
   * @internal
   */
  getNextLeaf(recordOrId, wrap = false) {
    const me = this, records = me.leaves, record = me.getById(recordOrId);
    let idx = records.indexOf(record);
    if (idx >= records.length - 1) {
      if (wrap) {
        idx = -1;
      } else {
        return null;
      }
    }
    return records[idx + 1];
  }
  /**
   * Finds the previous record among leaves (in a tree structure)
   * @param {Core.data.Model|String|Number} recordOrId Current record or its id
   * @param {Boolean} [wrap] Wrap at start/end or stop there
   * @returns {Core.data.Model} Previous record or null if current is the last one
   * @category Traverse
   * @internal
   */
  getPrevLeaf(recordOrId, wrap = false) {
    const me = this, records = me.leaves, record = me.getById(recordOrId);
    let idx = records.indexOf(record);
    if (idx === 0) {
      if (wrap) {
        idx = records.length;
      } else {
        return null;
      }
    }
    return records[idx - 1];
  }
  /**
   * Gets the next or the previous record among leaves (in a tree structure). Optionally wraps from first -> last and
   * vice versa
   * @param {String|Core.data.Model} recordOrId Record or record id
   * @param {Boolean} [next] Next (true) or previous (false)
   * @param {Boolean} [wrap] Wrap at start/end or stop there
   * @returns {Core.data.Model}
   * @category Traverse
   * @internal
   */
  getAdjacentLeaf(recordOrId, next = true, wrap = false) {
    return next ? this.getNextLeaf(recordOrId, wrap) : this.getPrevLeaf(recordOrId, wrap);
  }
  //endregion
};
//region Config & properties
__publicField(_Store, "$name", "Store");
__publicField(_Store, "configurable", {
  /**
   * Store's unique identifier.
   *
   * @member {String|Number} id
   * @readonly
   * @category Common
   */
  /**
   * Store's unique identifier. When set the store is added to a store map accessible through
   * `Store.getStore(id)`.
   *
   * @config {String|Number}
   * @category Common
   */
  id: true,
  /**
   * Class used to represent records in the store, should be a subclass of {@link Core.data.Model}. Only
   * applies when supplying data to the store (load, add), any supplied record instances are kept as is.
   *
   * ```javascript
   * class MyModel extends Model {
   *     static get fields() {
   *         return [
   *             'name',
   *             'city',
   *             'company'
   *         ]
   *     }
   * }
   *
   * const store = new Store({
   *     modelClass : MyModel,
   *     data : [
   *         { id : 1, name : 'Mark', city : 'London', company : 'Cool inc' },
   *         ...
   *     ]
   * });
   * ```
   *
   * @config {Core.data.Model}
   * @default
   * @typings {typeof Model}
   * @category Common
   */
  modelClass: Model,
  /**
   * Verify that loaded data does not contain any generated ids. If it does, a warning is logged on console.
   *
   * Set this to `false` to disable the check and give a very minor performance boost.
   *
   * @prp {Boolean}
   * @default
   */
  verifyNoGeneratedIds: true,
  /**
   * If set to `true`, or a config object, this makes the store load new records when needed. When a record that
   * is not already loaded is requested, the {@link #config-requestData} function is called. Please read the
   * [guide](#Grid/guides/data/lazyloading.md) to learn more on how to configure lazy loading.
   *
   * {@note}LazyLoading is currently not supported in Calendar, Gantt, TaskBoard.{/@note}
   *
   * @param {Object} lazyLoad Lazy load config
   * @param {Number} lazyLoad.chunkSize The number of records to be loaded before and after the requested index.
   * @prp {Boolean|Object}
   * @typings {Boolean|Object} // Fix for parent classes with other lazyLoad declarations
   * @category Common
   */
  lazyLoad: null,
  /**
   * For a non-AjaxStore, the autoLoad config will only take effect if the store is configured as
   * {@link #config-lazyLoad}, {@link #config-remoteSort}, {@link #config-remoteFilter} or
   * {@link #config-remotePaging}. In these cases, it will immediately after creation, perform a request by
   * calling the {@link #config-requestData} function.
   * @config {Boolean}
   * @category Common
   */
  autoLoad: null,
  /**
   * In a non-{@link Core.data.AjaxStore}, configured with {@link #config-lazyLoad}, {@link #config-remoteSort},
   * {@link #config-remoteFilter} or {@link #config-remotePaging}, the function provided here is called when the Store
   * needs new data, which will happen:
   *
   * * for {@link #config-lazyLoad}, when a record that has not yet been loaded is requested.
   * * for {@link #config-remoteSort}, on a sort operation.
   * * for {@link #config-remoteFilter}, on a filter operation.
   * * for {@link #config-remotePaging}, when current page is changed.
   *
   * When implementing this, it is expected that what is returned is an object with a `data` property containing the
   * records requested. What is requested will be specified in the `params` object, which will differ depending on the
   * source of the request.
   *
   * For {@link #config-lazyLoad}, the params object will contain `a startIndex` and a `count` param. It is expected
   * for the implementation of this function to provide a `data` property containing the number of records specified
   * in the `count` param starting from the specified `startIndex`.
   *
   * ```javascript
   * class MyStore extends Store {
   *    async requestData({startIndex, count}){
   *       const response = await getData(startIndex, count);
   *       return {
   *          data : response.records,
   *          total : response.totalRecordCount
   *       }
   *    }
   * }
   * ```
   *
   * For {@link #config-remotePaging}, the params object will contain a `page` and a `pageSize` param. It is expected
   * for the implementation of this function to provide a `data` property containing the number of records specified
   * in the `pageSize` param starting from the specified `page`.
   *
   * ```javascript
   * class MyStore extends Store {
   *    requestData({page, pageSize}){
   *       const start = (page - 1) * pageSize;
   *       const data = allRecords.splice(start, start + pageSize);
   *
   *       return {
   *          data,
   *          total : allRecords.length
   *       }
   *    }
   * }
   * ```
   *
   * For {@link #config-lazyLoad} it is recommended, and for {@link #config-remotePaging} it is required, to include a
   * `total` property which reflects the total amount of records available to load. If the `total` property is omitted
   * (when {@link #config-lazyLoad}), certain features and functions are disabled:
   *
   * * The component (Grid for example) is not aware of the total number of records, which will make the scrollbar's
   *   thumb change size and position when new records are loaded.
   * * The store don't know when to stop requesting new records. The `total` property will be set to the index of the
   *   last record loaded after requestData returns with fewer records than requested.
   *
   * If {@link #config-remoteSort} is active, the params object will contain a `sorters` param, containing a number of
   * sorter objects.The sorter objects will look like this:
   *
   * ```javascript
   * {
   *     "field": "name",
   *     "ascending": true
   * }
   * ```
   *
   * If {@link #config-remoteFilter} is active, the params object will contain a `filters` param, containing a number
   * of filters objects. The filter objects will look like this:
   *
   * ```javascript
   * {
   *     "field": "country",
   *     "operator": "=",
   *     "value": "sweden",
   *     "caseSensitive": false
   * }
   * ```
   *
   * The Base implementation of this function does nothing, you need to create your own subclass with an
   * implementation.
   *
   * Configuration parameter to pass a function to be used as {@link #config-requestData} in non-AjaxStore.
   *
   * @config {Function}
   * @param {LazyLoadRequestParams|PagingRequestParams} params Object containing info of which records is requested
   * @returns {DataLoadRequestResponse}
   * @async
   * @category Common
   */
  requestData: null,
  /**
   * Retools the loaded data objects instead of making shallow copies of them. This increases performance but
   * pollutes the incoming data.
   *
   * This setting is off by default, but is turned on automatically unless explicitly configured when data is
   * loaded using an `AjaxStore` (configured with a `readUrl`) or a `CrudManager` (configured to load data
   * remotely).
   *
   * ```javascript
   * // No duplicate id checking, no type conversions
   * new Store({ useRawData : true });
   * ```
   *
   * Also allows disabling certain steps in data loading, to further improved performance. Either accepts an
   * object with the params described below or `true` which enables `disableDuplicateIdCheck`.
   *
   * ```javascript
   * new Store({
   *   // No type conversions only
   *   useRawData : {
   *     disableTypeConversion : true
   *   }
   * });
   * ```
   *
   * Note that if the first object in the incoming data is determined to be un-extensible (~immutable),
   * the entire incoming dataset will be cloned.
   *
   * Also note that since incoming data objects gets polluted with this setting on, reusing the same data
   * objects elsewhere might lead to unexpected behavior.
   *
   * {@note}When binding to data in frameworks with this setting enabled, the same principles as for framework
   * state applies - an object in the incoming data must be replaced instead of mutated for a change to be
   * detected. See for example [React's explanation here](https://react.dev/learn/updating-objects-in-state)
   * {/@note}
   *
   * @config {Boolean|Object}
   * @param {Boolean} [disableDuplicateIdCheck] Data must not contain duplicate ids, check is bypassed.
   * @param {Boolean} [disableDefaultValue] Default values will not be applied to record fields.
   * @param {Boolean} [disableTypeConversion] No type conversions will be performed on record data. Incoming
   * data must be in the correct format expected by each field, for example dates must be Date objects.
   * @category Advanced
   * @non-lazy-load
   */
  useRawData: useRawDataUnset,
  /**
   * Automatically detect from set data if used as tree store or flat store. If {@link #config-tree} is explicitly
   * set to `false`, then this value will be overruled and no tree detection performed.
   * @config {Boolean}
   * @default
   * @category Tree
   * @non-lazy-load
   */
  autoTree: true
});
var Store = _Store;
Store.initClass();
Store._$name = "Store";

// ../Core/lib/Core/data/plugin/AjaxStoreLazyLoadPlugin.js
var AjaxStoreLazyLoad = class extends StoreLazyLoadPlugin {
  // There's no merging of pluginConfigs
  static get pluginConfig() {
    const config = {};
    for (const prop in super.pluginConfig) {
      config[prop] = [...super.pluginConfig[prop]];
    }
    config.assign.push(
      "updateResponseTotalProperty",
      "updateResponseDataProperty"
    );
    return config;
  }
  // AjaxStore's totalProperty will apply for lazyLoading
  updateResponseTotalProperty(name) {
    this.totalCountProperty = name;
  }
  // AjaxStore's dataProperty will apply for lazyLoading
  updateResponseDataProperty(name) {
    this.dataProperty = name;
  }
  // Scoped to run on the client
  async requestData(params) {
    const {
      totalCountProperty,
      dataProperty
    } = this.lazyLoad, resp = await this.internalLoad(params, "", void 0, false);
    return {
      [totalCountProperty]: resp.json[totalCountProperty],
      [dataProperty]: resp.data
    };
  }
  // Overridden to support params param
  async load(params) {
    const { client } = this, originalStoreParams = client.params;
    if (params) {
      client.params = {
        ...params,
        ...originalStoreParams
      };
    }
    if (client.autoLoad && client.isConstructing) {
      await new Promise((resolve) => this.requestAnimationFrame(resolve));
    }
    await super.load();
    if (params) {
      client.params = originalStoreParams;
    }
  }
};
__publicField(AjaxStoreLazyLoad, "$name", "AjaxStoreLazyLoadPlugin");
AjaxStoreLazyLoad._$name = "AjaxStoreLazyLoad";

// ../Core/lib/Core/data/AjaxStore.js
var processParamEntry = (paramArray, entry) => {
  if (Array.isArray(entry[1])) {
    entry[1].forEach((value2) => paramArray.push(encodeURIComponent(entry[0]) + "=" + encodeURIComponent(value2)));
  } else {
    paramArray.push(encodeURIComponent(entry[0]) + "=" + encodeURIComponent(entry[1]));
  }
  return paramArray;
};
var immediatePromise3 = Promise.resolve();
var prependZeros = (num) => {
  return String(num).padStart(2, "0");
};
var dateToString = (date2) => {
  return `${date2.getFullYear()}-${prependZeros(date2.getMonth() + 1)}-${prependZeros(date2.getDate())}T${prependZeros(date2.getHours())}:${prependZeros(date2.getMinutes())}:${prependZeros(date2.getSeconds())}.${date2.getMilliseconds()}`;
};
var extractPersistableData = (r) => r.persistableData;
var rejectEmpty = (el) => !ObjectHelper.isEmpty(el);
var AjaxStore = class extends Store {
  constructor() {
    super(...arguments);
    /**
     * Url to post newly created records to.
     *
     * The response must be in the form:
     *
     *     {
     *         "success": true,
     *         "data": [{
     *             "id": 0, "name": "General Motors"
     *         }, {
     *             "id": 1, "name": "Apple"
     *         }]
     *     }
     *
     * Just the array of data may be returned, however that precludes the orderly handling of errors encountered at the
     * server.
     *
     * If the server encountered an error, the packet would look like this:
     *
     *     {
     *         "success": false,
     *         "message": "Some kind of database error"
     *     }
     *
     * And that packet would be available in the {@link #event-exception} handler in the `response` property of the
     * event.
     *
     * The `success` property may be omitted, it defaults to `true`.
     *
     * @prp {String} createUrl
     * @category CRUD
     */
    /**
     * Url to read data from.
     *
     * The response must be in the form:
     *
     *     {
     *         "success": true,
     *         "data": [{
     *             "id": 0, "name": "General Motors"
     *         }, {
     *             "id": 1, "name": "Apple"
     *         }]
     *     }
     *
     * If the store {@link #property-isPaged is paged}, the total dataset size must be returned in the
     * {@link #config-responseTotalProperty} property:
     *
     *     {
     *         "success": true,
     *         "data": [{
     *             "id": 0, "name": "General Motors"
     *         }, {
     *             "id": 1, "name": "Apple"
     *         }],
     *         "total": 65535
     *     }
     *
     * Just the array of data may be returned, however that precludes the orderly handling of errors encountered at the
     * server.
     *
     * If the server encountered an error, the packet would look like this:
     *
     *     {
     *         "success": false,
     *         "message": "Some kind of database error"
     *     }
     *
     * And that packet would be available in the {@link #event-exception} handler in the `response` property of the
     * event.
     *
     * The `success` property may be omitted, it defaults to `true`.
     *
     * @prp {String} readUrl
     * @category CRUD
     */
    /**
     * Url to post record modifications to.
     *
     * The response must be in the form:
     *
     *     {
     *         "success": true,
     *         "data": [{
     *             "id": 0, "name": "General Motors"
     *         }, {
     *             "id": 1, "name": "Apple"
     *         }]
     *     }
     *
     * Just the array of data may be returned, however that precludes the orderly handling of errors encountered at the
     * server.
     *
     * If the server encountered an error, the packet would look like this:
     *
     *     {
     *         "success": false,
     *         "message": "Some kind of database error"
     *     }
     *
     * And that packet would be available in the {@link #event-exception} handler in the `response` property of the
     * event.
     *
     * The `success` property may be omitted, it defaults to `true`.
     *
     * @prp {String} updateUrl
     * @category CRUD
     */
    /**
     * Url for deleting records.
     *
     * The response must be in the form:
     *
     *     {
     *         "success": true
     *     }
     *
     * If the server encountered an error, the packet would look like this:
     *
     *     {
     *         "success": false,
     *         "message": "Some kind of database error"
     *     }
     *
     * And that packet would be available in the {@link #event-exception} handler in the `response` property of the
     * event.
     *
     * The `success` property may be omitted, it defaults to `true`.
     *
     * @prp {String} deleteUrl
     * @category CRUD
     */
    /**
     * True to initiate a load when the store is instantiated
     * @config {Boolean} autoLoad
     * @category Common
     */
    //endregion
    __publicField(this, "modificationsTracking", 0);
  }
  static get defaultConfig() {
    return {
      /**
       * A string keyed object containing the HTTP headers to add to each server request issued by this store.
       *
       * `AjaxStore` uses the Fetch API under the hood, read more about headers on
       * [MDN](https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch#headers)
       *
       * Example usage:
       *
       * ```javascript
       * const store = new AjaxStore({
       *    headers : {
       *        Authorization : 'auth-contents-goes-here'
       *    }
       * });
       * ```
       *
       * @config {Object<String,String>}
       * @category Remote
       */
      headers: null,
      /**
       * An object containing the Fetch options to pass to each server request issued by this store. Use this to
       * control if credentials are sent and other options, read more at
       * [MDN](https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch#supplying_request_options).
       *
       * Example usage:
       *
       * ```javascript
       * const store = new AjaxStore({
       *    fetchOptions : {
       *        credentials : 'omit',
       *        redirect    : 'error'
       *    }
       * });
       * ```
       *
       * @config {Object}
       * @category Remote
       */
      fetchOptions: null,
      /**
       * Specify `true` to send payloads as form data, `false` to send as regular JSON.
       * @config {Boolean}
       * @default false
       * @category Remote
       */
      sendAsFormData: null,
      /**
       * Specify `true` to send all model fields when committing modified records (as opposed to just the
       * modified fields)
       * @config {Boolean}
       * @default false
       * @category Remote
       */
      writeAllFields: null,
      /**
       * The name of the HTTP parameter passed to this Store's {@link #config-readUrl} to indicate the node `id`
       * to load when loading child nodes on demand if the node being expanded was created with data containing
       * `children: true`.
       * @config {String}
       * @default
       * @category Remote
       */
      parentIdParamName: "id",
      /**
       * Set to ´true´ to use restful {@link #config-httpMethods}
       * @config {Boolean}
       * @default false
       * @category Remote
       */
      useRestfulMethods: null,
      /**
       * The HTTP methods to use for CRUD requests when {@link #config-useRestfulMethods} is enabled.
       *
       * ```javascript
       * new AjaxStore({
       *    useRestfulMethods : true,
       *    httpMethods : {
       *        create : 'POST',
       *        read   : 'POST',
       *        update : 'PATCH',
       *        delete : 'DELETE'
       *    }
       * });
       *
       * ```
       * @config {HttpMethods}
       * @default
       * @category Remote
       */
      httpMethods: {
        create: "POST",
        read: "GET",
        update: "PUT",
        delete: "DELETE"
      },
      /**
       * When this config is enabled, the {@link #property-params} of "read" request are placed in the
       * request body instead of the query string, if the HTTP method allows.
       *
       * Default value is `false`, but it will be set to `true` from the 6.0.0 release.
       *
       * @config {Boolean}
       * @default false
       * @category Remote
       */
      paramsInBody: VersionHelper.checkVersion("core", "6.0", ">=")
    };
  }
  suspendModificationsTracking() {
    this.modificationsTracking++;
  }
  resumeModificationsTracking() {
    this.modificationsTracking--;
  }
  afterConstruct(config) {
    super.afterConstruct(config);
    if (this.autoLoad && this.readUrl) {
      this.load().catch(() => {
      });
    }
  }
  get $lazyLoadPluginClass() {
    return AjaxStoreLazyLoad;
  }
  /**
   * Returns a truthy value if the Store is currently loading.
   *
   * A load operation is initiated by a load call, but the network request is not sent until
   * after a delay until the next event loop because of allowing all operations which may
   * request a load to coalesce into one call.
   *
   * If the loading request is in this waiting state, the value will be `1`,
   *
   * If the network request is in flight, the value will be `2`
   * @property {Boolean|Number}
   * @readonly
   * @category CRUD
   */
  get isLoading() {
    return super.isLoading || this._isLoading ? 2 : this.loadTriggerPromise ? 1 : false;
  }
  /**
   * Returns true if the Store is currently committing
   * @property {Boolean}
   * @readonly
   * @category CRUD
   */
  get isCommitting() {
    var _a5;
    return Boolean((_a5 = this.commitPromise) != null ? _a5 : this.autoCommitTimeoutPromise);
  }
  updateFilterParamName(param) {
    super.updateFilterParamName(param);
    this.remoteFilter = this.remoteFilter || this.restfulFilter;
  }
  updateRestfulFilter(restfulFilter, old) {
    this.remoteFilter = restfulFilter || Boolean(this.filterParamName) || this.remoteFilter && !old;
  }
  buildQueryString(url, ...paramObjects) {
    const hasParamsInUrl = url.includes("?"), queryString = Object.entries(Object.assign({}, ...paramObjects)).reduce(processParamEntry, []).join("&");
    return queryString ? (hasParamsInUrl ? "&" : "?") + queryString : "";
  }
  /**
   * Internal sort method.
   * Should not be used in application code directly.
   * @param silent
   * @returns {Promise|null} If {@link Core/data/AjaxStore#config-sortParamName} is set on store, this method returns `Promise`
   * which is resolved after data is loaded from remote server, otherwise it returns `null`
   * @async
   * @internal
   */
  async performRemoteSort(silent) {
    var _a5;
    const me = this;
    if (me.readUrl && !me.isRemoteDataLoading) {
      me.isRemoteDataLoading = true;
      if (await ((_a5 = me.beforePerformSort) == null ? void 0 : _a5.call(me)) === false) {
        return;
      }
      const result = await me.load();
      me.afterPerformSort(silent);
      me.isRemoteDataLoading = false;
      return result;
    } else {
      super.performSort(silent);
    }
  }
  /**
   * Internal filter method.
   * Should not be used in application code directly.
   * @param silent
   * @returns {Promise|null} If {@link Core/data/AjaxStore#config-filterParamName} is set on store, this method returns `Promise`
   * which is resolved after data is loaded from remote server, otherwise it returns `null`
   * @async
   * @internal
   */
  async performRemoteFilter(silent) {
    var _a5, _b2;
    const me = this;
    if (me.filterBatching > 0) {
      me.needsFiltering = true;
      return;
    }
    if (me.readUrl) {
      (_a5 = me.loadingPromise) == null ? void 0 : _a5.abort();
      me.isRemoteDataLoading = true;
      const oldCount = me.count, { filters } = me;
      if (await ((_b2 = me.beforePerformFilter) == null ? void 0 : _b2.call(me, { filters, oldCount })) === false) {
        return;
      }
      const result = await me.load(), event = silent ? null : {
        action: "filter",
        filters,
        oldCount,
        records: me.storage.values
      };
      me.afterPerformFilter(event);
      me.trigger("refresh", event);
      me.isRemoteDataLoading = false;
      return result;
    } else {
      super.performRemoteFilter(silent);
    }
  }
  /**
   * A provided function which creates an array of values for the {@link #config-filterParamName} to pass
   * any filters to the server upon load.
   *
   * By default, this creates a JSON string containing the following properties:
   *
   * ```javascript
   *    [{
   *        field         : <theFieldName>
   *        operator      : May be: `'='`, `'!='`, `'>'`, `'>='`, `'<'`, `'<='`, `'*'`, `'startsWith'`, `'endsWith'`
   *        value         : The value to compare
   *        caseSensitive : true for case sensitive comparisons
   *    }]
   * ```
   * @param {Core.util.CollectionFilter[]} filters The filters to encode.
   */
  encodeFilterParams(filters) {
    const result = this.getFilterParams(filters);
    return JSON.stringify(result, function(key, value2) {
      return key === "" ? value2 : this[key] instanceof Date ? dateToString(this[key]) : value2;
    });
  }
  /**
   * A provided function which creates an array of values for the {#config-sortParamName} to pass
   * any sorters to the server upon load.
   *
   * By default, this creates a JSON string containing the following properties:
   *
   * ```javascript
   *    [{
   *        field     : <theFieldName>
   *        ascending : true/false
   *    }]
   * ```
   *
   * @param {Sorter[]} sorters The sorters to encode.
   */
  encodeSorterParams(sorters) {
    return JSON.stringify(this.getSorterParams(sorters));
  }
  buildReadUrl() {
    const { readUrl } = this;
    if (this.restfulFilter && this.filters.count) {
      const url = readUrl.endsWith("/") ? readUrl : readUrl + "/";
      return url + this.filters.first.value;
    }
    return readUrl;
  }
  /**
   * Internal data loading method.
   * @returns {Promise}
   * @internal
   */
  internalLoad(params, eventName, successFn, delay2 = 0) {
    params = ObjectHelper.assign({}, this.params, params);
    const me = this, event = { action: "read" + eventName, params, url: me.buildReadUrl() };
    me.buildRemoteParams(params, true);
    if (me.trigger("beforeLoad" + eventName, event) === false) {
      throw false;
    }
    me.loadArgs = [event, params, eventName, successFn];
    if (delay2 === false) {
      return new Promise((resolve, reject) => me.sendLoadRequest(resolve, reject));
    }
    return me.loadTriggerPromise || (me.loadTriggerPromise = new Promise((resolve, reject) => {
      me.setTimeout({
        delay: delay2,
        fn: "sendLoadRequest",
        args: [resolve, reject],
        cancelOutstanding: true
      });
    }));
  }
  // Send the request for the internalLoad.
  // This is called on a timeout 1ms after the internalLoad call.
  async sendLoadRequest(resolve, reject) {
    var _a5, _b2, _c2;
    const me = this, [
      event,
      params,
      eventName,
      successFn
    ] = me.loadArgs;
    me.loadTriggerPromise = null;
    if (event.url) {
      me._isLoading = true;
      me.trigger(`load${eventName}Start`, event);
      if (await me.trigger("beforeRequest", event) === false) {
        me._isLoading = false;
        resolve(event);
      }
      const options = { parseJson: true };
      if (me.headers) {
        options.headers = ObjectHelper.clone(me.headers);
      }
      if (me.useRestfulMethods) {
        options.method = me.httpMethods.read;
        if (event.body) {
          options.body = JSON.stringify(event.body);
        }
      }
      try {
        const placeParametersInBody = me.paramsInBody && (((_a5 = options.method) == null ? void 0 : _a5.toUpperCase()) === "POST" || ((_b2 = options.method) == null ? void 0 : _b2.toUpperCase()) === "PUT") && options.body == null, url = event.url + (placeParametersInBody ? "" : me.buildQueryString(event.url, params)), promise = me.loadingPromise = AjaxHelper.get(
          url,
          ObjectHelper.assign(options, me.fetchOptions, placeParametersInBody ? { body: JSON.stringify(params) } : null)
        ), response = await promise;
        if (me.isDestroyed) {
          return;
        }
        const data = me.transformLoadedData ? me.transformLoadedData(response.parsedJson) : response.parsedJson, isArray4 = Array.isArray(data), success2 = isArray4 || data && ObjectHelper.getPath(data, me.responseSuccessProperty) !== false, remoteTotal = isArray4 ? null : ObjectHelper.getPath(data, me.responseTotalProperty);
        me.loadingPromise = null;
        me._isLoading = false;
        event.response = response;
        event.json = data;
        if (success2) {
          if (remoteTotal != null) {
            me.remoteTotal = parseInt(remoteTotal, 10);
          }
          if (me.isPaged && !me.lazyLoad) {
            if (me.remoteTotal >= 0) {
              me.currentPage = me.pageStartParamName ? params[me.pageStartParamName] / me.pageSize + 1 : params[me.defaultPageParamName];
            } else {
              throw new Error("A paged store must receive its responseTotalProperty in each data packet");
            }
          }
          event.data = isArray4 ? data : ObjectHelper.getPath(data, me.responseDataProperty);
          me.suspendModificationsTracking();
          await (successFn == null ? void 0 : successFn(event));
          me.resumeModificationsTracking();
          if (me.isPartOfProjectRaw) {
            if (me.ignoreLoadPropagationChanges) {
              me.suspendModificationsTracking();
            }
            await ((_c2 = me.getProject()) == null ? void 0 : _c2.commitAsync());
            if (me.ignoreLoadPropagationChanges) {
              me.resumeModificationsTracking();
            }
          }
          !me.isDestroyed && me.trigger("load" + eventName, event);
          resolve(event);
        } else {
          Object.assign(event, {
            exception: true,
            exceptionType: "server",
            error: data == null ? void 0 : data.error
          });
          !me.isDestroyed && me.trigger("exception", event);
          reject(event);
        }
      } catch (responseOrError) {
        me._isLoading = false;
        event.exception = true;
        if (responseOrError instanceof Response) {
          event.exceptionType = responseOrError.ok ? "server" : "network";
          event.response = responseOrError;
          event.error = responseOrError.error;
        } else {
          event.exceptionType = "server";
          event.error = responseOrError;
        }
        !me.isDestroyed && me.trigger("exception", event);
        reject(event);
      } finally {
        !me.isDestroyed && me.trigger("afterRequest", event);
      }
    }
  }
  /**
   * Load data from the {@link #config-readUrl}. If configured as {@link #config-lazyLoad}, this will load 1 chunk of
   * records starting from index 0. If the store has previously been loaded, it will first be cleared of all records
   * and all lazy load cache.
   * @param {Object} [params] A hash of parameters to append to querystring (will also append Store#params)
   * @returns {Promise} A Promise which will be resolved if the load succeeds, and rejected if the load is
   * vetoed by a {@link #event-beforeLoad} handler, or if an {@link #event-exception} is detected.
   * The resolved function is passed the event object passed to any event handlers.
   * The rejected function is passed the {@link #event-exception} event if an exception occurred,
   * or `false` if the load was vetoed by a {@link #event-beforeLoad} handler.
   * @fires beforeLoad
   * @fires loadStart
   * @fires beforeRequest
   * @fires load
   * @fires exception
   * @fires afterRequest
   * @category CRUD
   */
  async load(params) {
    const me = this;
    if (me.isPaged) {
      return me.loadPage(me.currentPage || 1, params);
    } else {
      return me.internalLoad(params, "", (event) => {
        me.data = event.data;
      });
    }
  }
  /**
   * Loads children into specified parent record. Parent records id is sent as a param (param name configured with
   * {@link #config-parentIdParamName}.
   * @param {Core.data.Model} parentRecord Parent record
   * @returns {Promise} A Promise which will be resolved if the load succeeds, and rejected if the load is
   * vetoed by a {@link #event-beforeLoadChildren} handler, or if an {@link #event-exception} is detected.
   * The resolved function is passed the event object passed to any event handlers.
   * The rejected function is passed the {@link #event-exception} event if an exception occurred,
   * or `false` if the load was vetoed by a {@link #event-beforeLoadChildren} handler.
   * @fires beforeLoadChildren
   * @fires loadChildrenStart
   * @fires beforeRequest
   * @fires loadChildren
   * @fires exception
   * @fires afterRequest
   * @category CRUD
   * @non-lazy-load
   */
  async loadChildren(parentRecord) {
    return this.readUrl ? this.internalLoad({ [this.parentIdParamName]: parentRecord.id }, "Children", (event) => {
      event.parentRecord = parentRecord;
      if (parentRecord.children.length) {
        parentRecord.clearChildren(true);
      }
      parentRecord.data[parentRecord.constructor.childrenField] = event.data;
      parentRecord.processChildren(parentRecord.stores);
    }, false) : this.immediatePromise;
  }
  //region Paging
  /**
   * Loads a page of data from the {@link #config-readUrl}.
   * @param {Number} page The *one based* page number to load.
   * @param {Object} params A hash of parameters to append to querystring (will also append Store#params)
   * @returns {Promise} A Promise which will be resolved if the load succeeds, and rejected if the load is
   * vetoed by a {@link #event-beforeLoadPage} handler, or if an {@link #event-exception} is detected.
   * The resolved function is passed the event object passed to any event handlers.
   * The rejected function is passed the {@link #event-exception} event if an exception occurred,
   * or `false` if the load was vetoed by a {@link #event-beforeLoadPage} handler.
   * @fires beforeLoadPage
   * @fires loadPageStart
   * @fires beforeRequest
   * @fires loadPage
   * @fires exception
   * @fires afterRequest
   * @category CRUD
   * @non-lazy-load
   */
  async loadPage(page, params = {}) {
    if (!this.readUrl) {
      return super.loadPage(page);
    }
    params[this.defaultPageParamName] = page != null ? page : 1;
    return this.internalLoad(params, "Page", (event) => {
      this.loadData(event.data, "pageLoad");
    });
  }
  /**
   * If this store {@link #property-isPaged is paged}, and is not already at the {@link #property-lastPage}
   * then this will load the next page of data.
   * @fires beforeLoadPage
   * @fires loadPageStart
   * @fires beforeRequest
   * @fires loadPage
   * @fires exception
   * @fires afterRequest
   * @category CRUD
   * @returns {Promise} A promise which is resolved when the Ajax request completes and has been processed.
   * @non-lazy-load
   */
  /**
   * If this store {@link #property-isPaged is paged}, and is not already at the first page
   * then this will load the previous page of data.
   * @fires beforeLoadPage
   * @fires loadPageStart
   * @fires beforeRequest
   * @fires loadPage
   * @fires exception
   * @fires afterRequest
   * @category CRUD
   * @returns {Promise} A promise which is resolved when the Ajax request completes and has been processed.
   * @non-lazy-load
   */
  //endregion
  //region Commit
  /**
   * Commits all changes (added, modified and removed) using corresponding urls ({@link #config-createUrl},
   * {@link #config-updateUrl} and {@link #config-deleteUrl})
   * @fires beforeCommit
   * @returns {Promise|Boolean} A Promise which is resolved only if all pending changes (Create, Update and Delete)
   * successfully resolve. Both the resolve and reject functions are passed a `commitState` object which is stored the
   * {@link #event-afterRequest} event for each request. Each event contains the `exception`, `request` and `response`
   * properties eg:
   *
   * ```javascript
   * {
   *      // If *all* commits succeeded
   *      success: true,
   *      changes: {
   *          added: [records...],
   *          modified: [records...],
   *          removed: [records...],
   *      },
   *      added: {
   *          source: theStore,
   *
   *          // Only if the add request triggered an exception
   *          exception: true,
   *
   *          // Only if the add request triggered an exception
   *          exceptionType: 'server', // Or 'network'
   *
   *          response: Response,
   *          json: parsedResponseObject
   *      },
   *      // Same format as added
   *      modified: {},
   *      removed: {}
   * }
   * ```
   *
   * If there were no pending changes, the resolve and reject functions are passed no parameters.
   *
   * If a commit operation already is in progress, a new one will be queued to execute after.
   * The resolved function is passed the event object passed to any event handlers.
   * The rejected function is passed the {@link #event-exception} event if an exception occurred.
   * @category CRUD
   */
  async commit() {
    this.clearTimeout("autoCommit");
    this.autoCommitTimeoutPromise = null;
    return this.queueCommit();
  }
  /**
   * Internally used to handle the commit queue
   * @returns {Promise}
   * @internal
   */
  // Makes sure there is only one commit in progress
  // Queues the commits that happens while committing
  queueCommit() {
    const me = this;
    let queuePromise;
    if (me.commitPromise) {
      queuePromise = me.commitPromise.then(() => me.performCommit());
    } else {
      me._commitPromiseQueueLength = 0;
      queuePromise = me.performCommit();
    }
    me._commitPromiseQueueLength += 1;
    return me.commitPromise = queuePromise == null ? void 0 : queuePromise.finally(() => {
      me._commitPromiseQueueLength -= 1;
      if (me._commitPromiseQueueLength === 0) {
        me.commitPromise = null;
      }
    });
  }
  /**
   * Internally used to perform the actual commit without any queue handling or timeout
   * @returns {Promise}
   * @internal
   */
  performCommit() {
    const me = this, { changes } = me, allPromises = [];
    if (!changes) {
      if (me.modified.count) {
        me.modified.forEach((record) => record.internalClearChanges(true, false));
        me.modified.clear();
      }
      return immediatePromise3;
    }
    if (me.trigger("beforeCommit", { changes }) !== false) {
      [...changes.added, ...changes.modified, ...changes.removed].forEach((record) => record.meta.committing = true);
      me.trigger("commitStart", { changes });
      const commitState = {
        action: "commit",
        exception: false,
        changes
      };
      let p = me.commitRemoved(commitState);
      if (p) {
        allPromises.push(p);
      }
      p = me.commitAdded(commitState);
      if (p) {
        allPromises.push(p);
      }
      p = me.commitModified(commitState);
      if (p) {
        allPromises.push(p);
      }
      if (!allPromises.length) {
        me.modified.forEach((r) => r.internalClearChanges(true, false));
        me.modified.clear();
        me.added.forEach((r) => r.internalClearChanges(true, false));
        me.added.clear();
        me.removed.clear();
        me.trigger("commit", { changes });
        return immediatePromise3;
      }
      p = allPromises.length === 1 ? allPromises[0] : Promise.all(allPromises);
      return new Promise((resolve, reject) => {
        p.then(() => {
          if (commitState.exception) {
            me.trigger("exception", commitState);
            reject(commitState);
          } else {
            me.trigger("commit", { changes });
            resolve(commitState);
          }
        }).catch(() => {
          reject(commitState);
        });
      });
    } else {
      return immediatePromise3;
    }
  }
  // Performs background autocommit with reject checking
  doAutoCommit() {
    const me = this;
    if (me.suspendCount <= 0 && !me.hasTimeout("autoCommit")) {
      me.autoCommitTimeoutPromise = new Promise((resolve) => {
        me.setTimeout({
          name: "autoCommit",
          fn: () => {
            me.autoCommitTimeoutPromise = null;
            me.commit().then().catch((commitState) => {
              const { response } = commitState;
              if (!(response && (response.ok && response.parsedJson || [500, 404].includes(response.status)))) {
                throw commitState;
              }
            });
            resolve();
          },
          delay: me.autoCommitTimeout
        });
      });
    }
  }
  buildCommitUrl(baseUrl) {
    return baseUrl + this.buildQueryString(baseUrl, this.params);
  }
  /**
   * Commits added records by posting to {@link #config-createUrl}.
   * Server should return a JSON object with a 'success' property indicating whether the operation was successful.
   * @param {Object} commitState An object into which is added a `delete` property being the
   * {@link #event-afterRequest} event.
   * @returns {Promise|null} If there are added records, a Promise which will be resolved whether the commit
   * succeeds or fails. The resulting event is placed into the `add` property of the passed `commitState`
   * parameter. If there are no added records, `null` is returned.
   * The resolved function is passed the event object passed to any event handlers.
   * @async
   * @private
   * @fires beforeRequest
   * @fires commitAdded
   * @fires refresh
   * @fires exception
   * @fires afterRequest
   */
  commitAdded(commitState) {
    return this.added.count && this.createUrl ? this.commitAddedAsync(commitState) : null;
  }
  async commitAddedAsync(commitState) {
    const me = this, added = Array.from(me.added), toAdd = added.map(extractPersistableData), event = { action: "create", params: me.params, url: me.buildCommitUrl(me.createUrl) };
    commitState.create = event;
    event.body = { data: toAdd };
    if (await me.trigger("beforeRequest", event) === false) {
      added.forEach((r) => r.meta.committing = false);
      return commitState;
    }
    let dataToSend = me.transformCreationData ? me.transformCreationData(event.body) : event.body;
    if (me.sendAsFormData) {
      const formData = new FormData();
      formData.append("data", JSON.stringify(dataToSend.data));
      dataToSend = formData;
    }
    const options = { parseJson: true };
    if (me.headers) {
      options.headers = ObjectHelper.clone(me.headers);
    }
    if (me.useRestfulMethods) {
      options.method = me.httpMethods.create;
    }
    try {
      const response = await AjaxHelper.post(event.url, dataToSend, ObjectHelper.assign(options, me.fetchOptions));
      if (me.isDestroyed) {
        return;
      }
      const data = response.parsedJson, success2 = Array.isArray(data) || data && data.success !== false;
      commitState.response = response;
      event.json = data;
      event.response = response;
      if (success2) {
        const acceptedRecords = me.processReturnedData(data, added, toAdd);
        me.added.remove(acceptedRecords);
        me.modified.add(acceptedRecords.filter((a) => a.meta.modified && !ObjectHelper.isEmpty(a.meta.modified)));
        me.trigger("commitAdded");
        me.trigger("refresh", event);
      } else {
        added.forEach((r) => r.meta.committing = false);
        commitState.exception = event.exception = true;
        commitState.exceptionType = event.exceptionType = "server";
        me.trigger("exception", event);
      }
    } catch (responseOrError) {
      added.forEach((r) => r.meta.committing = false);
      commitState.exception = event.exception = true;
      if (responseOrError instanceof Response) {
        commitState.response = responseOrError;
        event.exceptionType = responseOrError.ok ? "server" : "network";
        event.response = responseOrError;
        event.error = responseOrError.error;
      } else {
        event.exceptionType = "server";
        event.error = responseOrError;
      }
      !me.isDestroyed && me.trigger("exception", event);
    } finally {
      !me.isDestroyed && me.trigger("afterRequest", event);
    }
    return commitState;
  }
  /**
   * Commits modified records by posting to {@link #config-updateUrl}.
   * Server should return a JSON object with a 'success' property indicating whether the operation was successful.
   * @param {Object} commitState An object into which is added a `delete` property being the
   * {@link #event-afterRequest} event.
   * @returns {Promise|null} If there are added records, a Promise which will be resolved whether the commit
   * succeeds or fails. The resulting event is placed into the `update` property of the passed `commitState`
   * parameter. If there are no added records, `null` is returned.
   * The resolved function is passed the event object passed to any event handlers.
   * @async
   * @private
   * @fires beforeRequest
   * @fires commitModified
   * @fires refresh
   * @fires exception
   * @fires afterRequest
   */
  commitModified(commitState) {
    return this.modified.count && this.updateUrl ? this.commitModifiedAsync(commitState) : null;
  }
  async commitModifiedAsync(commitState) {
    const me = this, modified = Array.from(me.changes.modified), event = { action: "update", params: me.params, url: me.buildCommitUrl(me.updateUrl) }, modifications = modified.map((record) => {
      if (me.writeAllFields) {
        return record.persistableData;
      } else {
        return record.modificationDataToWrite;
      }
    }).filter(rejectEmpty);
    if (modifications.length === 0) {
      me.modified.clear();
      modified.forEach((r) => r.meta.committing = false);
      return;
    }
    commitState.update = event;
    event.body = { data: modifications };
    if (await me.trigger("beforeRequest", event) === false) {
      return commitState;
    }
    let dataToSend = me.transformModificationData ? me.transformModificationData(event.body) : event.body;
    if (me.sendAsFormData) {
      const formData = new FormData();
      formData.append("data", JSON.stringify(dataToSend.data));
      dataToSend = formData;
    }
    const options = { parseJson: true };
    if (me.headers) {
      options.headers = ObjectHelper.clone(me.headers);
    }
    if (me.useRestfulMethods) {
      options.method = me.httpMethods.update;
    }
    try {
      const response = await AjaxHelper.post(
        event.url,
        dataToSend,
        ObjectHelper.assign(options, me.fetchOptions)
      );
      if (me.isDestroyed) {
        return;
      }
      const data = response.parsedJson, success2 = Array.isArray(data) || data && data.success !== false;
      commitState.response = response;
      event.json = data;
      event.response = response;
      if (success2) {
        me.processReturnedData(data, modified, modifications, true, true);
        me.trigger("commitModified");
        me.trigger("refresh", event);
      } else {
        modified.forEach((r) => r.meta.committing = false);
        commitState.exception = event.exception = true;
        event.exceptionType = "server";
        me.trigger("exception", event);
      }
    } catch (responseOrError) {
      modified.forEach((r) => r.meta.committing = false);
      commitState.exception = event.exception = true;
      if (responseOrError instanceof Response) {
        commitState.response = responseOrError;
        event.exceptionType = responseOrError.ok ? "server" : "network";
        event.response = responseOrError;
        event.error = responseOrError.error;
      } else {
        event.exceptionType = "server";
        event.error = responseOrError;
      }
      !me.isDestroyed && me.trigger("exception", event);
    } finally {
      !me.isDestroyed && me.trigger("afterRequest", event);
    }
    if (!modified.length && me.modified.count) {
      me.modified.clear();
    }
    return commitState;
  }
  // Used by commitAdded and commitModified to apply data changeset and clearing changes
  processReturnedData(returnedData, localRecords, changes, isUpdating = false) {
    if (!Array.isArray(returnedData)) {
      returnedData = ObjectHelper.getPath(returnedData, this.responseDataProperty);
    }
    const { idField } = localRecords[0].constructor, acceptAll = !returnedData || returnedData.length === localRecords.length, acceptedRecords = [];
    localRecords.forEach((record, i) => {
      const recData = isUpdating ? returnedData == null ? void 0 : returnedData.find((d) => d[idField] === record.id) : returnedData == null ? void 0 : returnedData[i];
      if (acceptAll || recData) {
        const currentChanges = record.modificationDataToWrite, committedData = isUpdating ? changes.find((m) => m[idField] === record.id) : changes[i], remapped = {};
        record.meta.committing = false;
        if (recData) {
          record.syncId(recData[idField]);
          Object.assign(record.data, this.modelClass.processData(recData, isUpdating, this, record));
        }
        if (currentChanges) {
          for (const key in committedData) {
            if (currentChanges[key] === committedData[key]) {
              const field = record.getFieldDefinitionFromDataSource(key);
              if (field == null ? void 0 : field.persist) {
                remapped[field.name] = 1;
              }
            }
          }
        }
        record.internalClearChanges(true, true, remapped, true);
        acceptedRecords.push(record);
      }
    });
    return acceptedRecords;
  }
  /**
   * Commits removed records by posting to {@link #config-deleteUrl}.
   * Server should return a JSON object with a 'success' property indicating whether the operation was successful.
   * @param {Object} commitState An object into which is added a `delete` property being the
   * {@link #event-afterRequest} event.
   * @returns {Promise|null} If there are added records, a Promise which will be resolved whether the commit
   * succeeds or fails. The resulting event is placed into the `delete` property of the passed `commitState`
   * parameter. If there are no added records, `null` is returned.
   * The resolved function is passed the event object passed to any event handlers.
   * @async
   * @private
   * @fires beforeRequest
   * @fires commitRemoved
   * @fires refresh
   * @fires exception
   * @fires afterRequest
   */
  commitRemoved(commitState) {
    return this.removed.count && this.deleteUrl ? this.commitRemovedAsync(commitState) : null;
  }
  async commitRemovedAsync(commitState) {
    var _a5, _b2;
    const me = this, {
      removed,
      includeChildrenInRemoveRequest
    } = me;
    let removedRecords;
    if (!includeChildrenInRemoveRequest) {
      removedRecords = [];
      for (const record of removed.values) {
        const parentId = (_b2 = record.parentId) != null ? _b2 : (_a5 = record.meta.modified) == null ? void 0 : _a5.parentId;
        if (parentId != null && (parentId === me.rootNode.id || !removed.includes(parentId))) {
          removedRecords.push(record);
        }
      }
    } else {
      removedRecords = removed.values;
    }
    const event = {
      action: "delete",
      params: me.params,
      url: me.buildCommitUrl(me.deleteUrl),
      body: { ids: removedRecords.map((r) => r.id) }
    };
    commitState.delete = event;
    if (await me.trigger("beforeRequest", event) === false) {
      return commitState;
    }
    let dataToSend = me.transformRemovalData ? me.transformRemovalData(event.body) : event.body;
    if (me.sendAsFormData) {
      const formData = new FormData();
      formData.append("id", JSON.stringify(dataToSend.ids));
      dataToSend = formData;
    }
    const options = { parseJson: true };
    if (me.headers) {
      options.headers = ObjectHelper.clone(me.headers);
    }
    if (me.useRestfulMethods) {
      options.method = me.httpMethods.delete;
    }
    try {
      const response = await AjaxHelper.post(
        event.url,
        dataToSend,
        ObjectHelper.assign(options, me.fetchOptions)
      );
      const data = response.parsedJson, isArray4 = Array.isArray(data), success2 = isArray4 || data && data.success !== false;
      commitState.response = response;
      event.json = data;
      event.response = response;
      if (success2) {
        removed.forEach((record) => record.meta.committing = false);
        removed.clear();
        me.trigger("commitRemoved");
        me.trigger("refresh", event);
      } else {
        removed.forEach((r) => r.meta.committing = false);
        commitState.exception = event.exception = true;
        event.exceptionType = "server";
        me.trigger("exception", event);
      }
    } catch (responseOrError) {
      removed.forEach((r) => r.meta.committing = false);
      commitState.exception = event.exception = true;
      if (responseOrError instanceof Response) {
        commitState.response = responseOrError;
        event.exceptionType = responseOrError.ok ? "server" : "network";
        event.response = responseOrError;
        event.error = responseOrError.error;
      } else {
        event.exceptionType = "server";
        event.error = responseOrError;
      }
      !me.isDestroyed && me.trigger("exception", event);
    } finally {
      !me.isDestroyed && me.trigger("afterRequest", event);
    }
    return commitState;
  }
  //endregion
};
__publicField(AjaxStore, "$name", "AjaxStore");
// region Events
/**
 * Fired when a remote request fails, either at the network level, or the server returns a failure, or an invalid
 * response.
 *
 * Note that when a {@link #function-commit} fails, more than one exception event will be triggered. The individual
 * operation, `create`, `update` or `delete` will trigger their own `exception` event, but the encapsulating commit
 * operation will also trigger an `exception` event when all the operations have finished, so if exceptions are
 * going to be handled gracefully, the event's `action` property must be examined, and the constituent operations of
 * the event must be examined.
 * @event exception
 * @param {Core.data.Store} source This Store
 * @param {Boolean} exception `true`
 * @param {'create'|'read'|'update'|'delete'|'commit'} action Action that failed, `'create'`, `'read'`,
 * `'update'` or `'delete'`. May also be fired with '`commit'` to indicate the failure of an aggregated `create`,
 * `update` and `delete` operation. In this case, the event will contain a property for each operation of the commit
 * named `'create'`, `'update'` and `'delete'`, each containing the individual `exception` events.
 * @param {'network'|'failure'} exceptionType The type of failure, `'network'` or `'server'`
 * @param {Response} response the `Response` object
 * @param {Object} json The decoded response object *if the exceptionType is `'server'`*
 */
/**
 * Fired after committing added records
 * @event commitAdded
 * @param {Core.data.Store} source This Store
 */
/**
 * Fired after committing modified records
 * @event commitModified
 * @param {Core.data.Store} source This Store
 */
/**
 * Fired on successful load
 * @event load
 * @param {Core.data.Store} source This Store
 * @param {Object[]} data Data loaded
 * @param {Response} response the `Response` object
 * @param {Object} json The decoded response object.
 */
/**
 * Fired on successful load of remote child nodes for a tree node.
 * @event loadChildren
 * @param {Core.data.Store} source This Store
 * @param {Object[]} data Data loaded
 * @param {Object} json The decoded response object.
 */
/**
 * Fired after committing removed records
 * @event commitRemoved
 * @param {Core.data.Store} source This Store
 */
/**
 * Fired before loading starts. Allows altering parameters and is cancelable by returning `false`. For paged stores,
 * instead listen to {@link #event-beforeLoadPage}. For remote loading of tree child nodes, listen to
 * {@link #event-beforeLoadChildren}.
 * @event beforeLoad
 * @preventable
 * @param {Core.data.Store} source This Store
 * @param {String} action The read action being performed: `'read'`
 * @param {String} url The URL to which the HTTP request will be sent. This property may be mutated in
 * an event handler *without changing* the base {@link #config-readUrl} configured for this Store.
 * @param {Object} params An object containing property/name pairs which are the parameters.
 * This may be mutated to affect the parameters used in the Ajax request.
 */
/**
 * Fired before loading of remote child nodes of a tree node starts. Allows altering parameters and is cancelable
 * @event beforeLoadChildren
 * @preventable
 * @param {Core.data.Store} source This Store
 * @param {String} action The read action being performed: `'readChildren'`
 * @param {String} url The URL to which the HTTP request will be sent. This property may be mutated in
 * an event handler *without changing* the base {@link #config-readUrl} configured for this Store.
 * @param {Object} params An object containing property/name pairs which are the parameters.
 * This may be mutated to affect the parameters used in the Ajax request.
 */
/**
 * When the store {@link #property-isPaged is paged}, this is fired when a page load is beginning.
 * @event loadPageStart
 * @param {Core.data.Store} source This Store
 * @param {Object} params An object containing property/name pairs which are the parameters.
 * This may be mutated to affect the parameters used in the Ajax request.
 */
/**
 * Fired when loading is beginning. This is not cancelable. Parameters in the event may still be
 * mutated at this stage.
 * @event loadStart
 * @param {Core.data.Store} source This Store
 * @param {Object} params An object containing property/name pairs which are the parameters.
 * This may be mutated to affect the parameters used in the Ajax request.
 */
/**
 * Fired when loading of remote child nodes into a tree node is beginning. This is not cancelable. Parameters in the
 * event may still be mutated at this stage.
 * @event loadChildrenStart
 * @param {Core.data.Store} source This Store
 * @param {Object} params An object containing property/name pairs which are the parameters.
 * This may be mutated to affect the parameters used in the Ajax request.
 */
// endregion
//region Config
__publicField(AjaxStore, "configurable", {
  /**
   * The optional property name in JSON responses from the server that contains a boolean
   * success/fail status.
   * ```json
   * {
   *   "responseMeta" : {
   *   {
   *     "success" : true,
   *     "count" : 100
   *   },
   *   // The property name used here should match that of 'responseDataProperty'
   *   "data" : [
   *     ...
   *   ]
   * }
   * ```
   *
   * The store would be configured with:
   * ```javascript
   *  {
   *      ...
   *      successDataProperty : 'responseMeta.success',
   *      responseTotalProperty : 'responseMeta.count'
   *      ...
   *  }
   *
   * ```
   * @config {String}
   * @default
   * @category Remote
   */
  responseSuccessProperty: "success",
  /**
   * The property name in JSON responses from the server that contains the data for the records
   * ```json
   * {
   *   "success" : true,
   *   // The property name used here should match that of 'responseDataProperty'
   *   "data" : [
   *     ...
   *   ]
   * }
   * ```
   * @config {String}
   * @default
   * @category Remote
   */
  responseDataProperty: "data",
  /**
   * The property name in JSON responses from the server that contains the dataset total size
   * **when this store {@link #property-isPaged is paged} or {@link #config-lazyLoad lazy loaded}**
   * ```json
   * {
   *   "success" : true,
   *   // The property name used here should match that of 'responseDataProperty'
   *   "data" : [
   *     ...
   *   ],
   *   // The property name used here should match that of 'responseTotalProperty'
   *   "total" : 65535
   * }
   * ```
   * @config {String}
   * @default
   * @category Remote
   */
  responseTotalProperty: "total",
  /**
   * An object containing key/value pairs that are passed on the request query string, or in the request body
   * if HTTP method allows. See {@link #config-paramsInBody} config.
   * @prp {Object}
   * @category Remote
   */
  params: null,
  /**
   * If set to `true`, or a config object, this makes the store load new records when needed. When a record that
   * is not already loaded is requested, a request to the configured {@link Core.data.AjaxStore#config-readUrl}
   * will be made.
   *
   * {@note}LazyLoading is currently not supported in Calendar, Gantt, TaskBoard.{/@note}
   *
   * @param {Object} lazyLoad Lazy load config
   * @param {Number} lazyLoad.chunkSize The number of records to be loaded before and after the requested index.
   * @prp {Boolean|Object} lazyLoad
   * @typings {Boolean|Object} // Fix for parent classes with other lazyLoad declarations
   */
  /**
   * The timeout in milliseconds to wait before persisting changes to the server.
   * Used when {@link #config-autoCommit} is set to `true`.
   * @config {Number}
   * @default
   * @category Common
   */
  autoCommitTimeout: 10,
  /**
   * Set to `false` to only include the id of a removed parent node in the request to the backend, and not the ids
   * of its children. Applies when configured with a {@link #config-deleteUrl}.
   *
   * Consider the following scenario, removing parent 1:
   *
   * ```javascript
   * {
   *   id : 1,
   *   children : [
   *     { id : 11 },
   *     { id : 12 }
   *   ]
   * }
   * ```
   *
   * With `includeChildrenInRemoveRequest : false`, the backend will only receive the removal of id 1.
   *
   * With `includeChildrenInRemoveRequest : true` (the default), the backend will receive the removal of id 1, 11
   * and 12.
   *
   * @config {Boolean}
   * @default true
   * @category Tree
   */
  includeChildrenInRemoveRequest: true,
  /**
   * Pass a function to transform data loaded from the backend before it is turned into records and added to the
   * store
   * ```javascript
   * new AjaxStore({
   *      transformLoadedData : response => {
   *          // Transform the loaded data and return the response object
   *      }
   * });
   * ```
   * @config {Function}
   * @param {Object} response
   * @param {Array} response.data The data that is going to be loaded into the store
   * @returns {Object} Returns the modified response object
   */
  transformLoadedData: null,
  /**
   * Pass a function to transform record creation data before it is sent to the backend
   * ```javascript
   * new AjaxStore({
   *      transformCreationData : payload => {
   *          // Transform the data and return the payload
   *          for (const creationData of payload.data) {
   *              creationData.timestamp = Date.now();
   *          }
   *
   *          return payload;
   *      }
   * });
   * ```
   * @config {Function}
   * @param {Object} payload
   * @param {Array} payload.data The data that is going to be sent to the backend
   * @returns {Object} Return the modified payload
   */
  transformCreationData: null,
  /**
   * Pass a function to transform records modification data before it is sent to the backend
   * ```javascript
   * new AjaxStore({
   *      transformModificationData : payload => {
   *          // Transform the data and return the payload
   *          for (const recordData of payload.data) {
   *              // Some kind of transformation here...
   *              if (recordData.ignore) {
   *                  ArrayHelper.remove(payload.data, recordData);
   *              }
   *          }
   *
   *          return payload;
   *      }
   * });
   * ```
   * @config {Function}
   * @param {Object} payload
   * @param {Array} payload.data The data that is going to be sent to the backend
   * @returns {Object} Return the modified payload
   */
  transformModificationData: null,
  /**
   * Pass a function to transform record removal data before it is sent to the backend
   * ```javascript
   * new AjaxStore({
   *      transformRemovalData : payload => {
   *          // Transform the data and return the payload
   *          const transformedData = {};
   *          for (const id of payload.ids) {
   *              transformedData[id] = true;
   *          }
   *          payload.ids = transformedData;
   *
   *          return payload;
   *      }
   * });
   * ```
   * @config {Function}
   * @param {Object} payload
   * @param {Array} payload.ids Ids for records being removed
   * @returns {Object} Return the modified payload
   */
  transformRemovalData: null,
  /**
   * This config is taken into account only when store is part of a scheduling project.
   *
   * If such store loads data via {@link Core.data.AjaxStore#config-readUrl}, automatic calculation
   * of the schedule after the load might lead to some data modifications (for example if {@link #config-syncDataOnLoad}
   * is enabled). Such modifications will then be submitted back to the server, which might not be needed,
   * since the data is already coming from the server.
   *
   * Enabling this config will ignore data modifications during the propagation stage and nothing
   * will be submitted to server.
   *
   * @config {Boolean} ignoreLoadPropagationChanges
   * @default
   */
  ignoreLoadPropagationChanges: false,
  /**
   * Set this flag to true if you are filtering remote using restful URLs (e.g.
   * https://nominatim.openstreetmap.org/search/paris?format=json)
   *
   * **Note:** When this is set, the filter string is appended to the readUrl.
   * @config {Boolean}
   * @category Remote
   */
  restfulFilter: false
});
AjaxStore._$name = "AjaxStore";

// ../Core/lib/Core/data/ComboModel.js
var ComboModel = class extends Model {
};
__publicField(ComboModel, "$name", "ComboModel");
__publicField(ComboModel, "fields", [
  /**
   * Value for the Combo's list record.
   * @field {String|Number|*} value
   */
  { name: "value" },
  /**
   * Display text for the Combo's list record.
   * @field {String} text
   */
  { name: "text" }
]);
ComboModel._$name = "ComboModel";

// ../Core/lib/Core/data/DomDataStore.js
var NOT_PASSED = {};
var DomDataStore = class {
  /**
   * Get the data that is connected to a specified `element` or just a specific `key`.
   * @param {HTMLElement} element DOM element
   * @param {String} [key] The name of the property in the element data to return
   * @returns {*}
   */
  static get(element, key = NOT_PASSED) {
    const result = element._domData || (element._domData = {});
    return key === NOT_PASSED ? result : result[key];
  }
  /**
   * Remove the specified `key` from the data connected to a given `element`.
   * @param {HTMLElement} element DOM element
   * @param {String} key The name of the property in the element data to remove.
   */
  static remove(element, key) {
    const { _domData } = element, was = _domData == null ? void 0 : _domData[key];
    _domData == null ? true : delete _domData[key];
    return was;
  }
  /**
   * Sets all data connected to specified element (completely replacing any existing) or just a specific key.
   * To update data, use DomDataStore#assign instead.
   * @param {HTMLElement} element DOM element
   * @param {Object|String} data Data object to set, or property name to set
   * @param {*} [value] If the previous parameter was a property name, this is the value to set.
   * @returns {*} The `value` or `data` passed.
   */
  static set(element, data, value2 = NOT_PASSED) {
    if (value2 === NOT_PASSED) {
      element._domData = value2 = data;
    } else {
      (element._domData || (element._domData = {}))[data] = value2;
    }
    return value2;
  }
  /**
   * Updates data connected to specified element.
   * @param {HTMLElement} element DOM element
   * @param {Object} data Data to assign
   */
  static assign(element, data) {
    Object.assign(element._domData || (element._domData = {}), data);
  }
};
DomDataStore._$name = "DomDataStore";

// ../Core/lib/Core/helper/CSSHelper.js
var CSSHelper = class {
  /**
   * Inserts a CSS style rule based upon the passed text
   * @param {String|String[]} cssText The text of the rule including selector and rule body just as it would
   * be specified in a CSS file.
   * @returns {CSSRule|CSSRule[]} The resulting `CSSRule` object(s) if the add was successful.
   */
  static insertRule(cssText, parentElement = document.head) {
    const styleSheet = this.getStyleSheet(parentElement), { cssRules } = styleSheet, oldCount = cssRules.length, one = typeof cssText === "string";
    (one ? [cssText] : cssText).forEach((rule, index) => styleSheet.insertRule(rule, index));
    if (cssRules.length > oldCount) {
      return one ? cssRules[0] : Array.from(cssRules).slice(0, cssText.length);
    }
  }
  /**
   * Looks up the first rule which matched the passed selector.
   * @param {String|Function} selector Either the selector string to exactly match or a function which
   * when passed a required selector, returns `true`.
   * @returns {CSSRule} The first matching CSS Rule object if any found.
   */
  static findRule(selector) {
    let result;
    const isFn = typeof selector === "function";
    Array.prototype.find.call(document.head.querySelectorAll("link[rel=stylesheet],style[type*=css]"), (element) => {
      result = Array.prototype.find.call(element.sheet.rules || element.sheet.cssRules, (r) => {
        return isFn ? selector(r) : r.selectorText === selector;
      });
      if (result) {
        return true;
      }
    });
    return result;
  }
  static getStyleSheet(parentElement = document.head) {
    if (!parentElement.$bryntumStylesheet) {
      parentElement.$bryntumStylesheet = DomHelper.createElement({
        tag: "style",
        type: "text/css",
        parent: parentElement
      }).sheet;
    }
    return parentElement.$bryntumStylesheet;
  }
  /**
   * Returns current CSS version
   * @returns {String}
   * @internal
   */
  static getCSSVersion() {
    return getComputedStyle(document.documentElement).getPropertyValue("--bryntum-version").replace(/[" ]/gm, "");
  }
};
__publicField(CSSHelper, "$name", "CSSHelper");
Helpers_default.register(CSSHelper);
CSSHelper._$name = "CSSHelper";

// ../Core/lib/Core/helper/mixin/DragHelperContainer.js
var DragHelperContainer_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    //region Init
    /**
     * Initialize container drag mode.
     * @private
     */
    initContainerDrag() {
      const me = this;
      if (!me.mode) {
        me.mode = "container";
      }
      if (me.mode === "container" && !me.containers) {
        throw new Error("Container drag mode must specify containers");
      }
    }
    //endregion
    //region Grab, update, finish
    /**
     * Grab an element which can be dragged between containers.
     * @private
     * @param event
     * @returns {Boolean}
     */
    grabContainerDrag(event) {
      const me = this;
      if (!me.ignoreSelector || !event.target.closest(me.ignoreSelector)) {
        const element = DomHelper.getAncestor(event.target, me.containers, me.outerElement);
        if (element) {
          const box = element.getBoundingClientRect();
          me.context = {
            element,
            position: DomHelper.getStyleValue(element, "position"),
            valid: true,
            action: "container",
            offsetX: event.pageX - box.left,
            offsetY: event.pageY - box.top,
            originalPosition: {
              parent: element.parentElement,
              prev: element.previousElementSibling,
              next: element.nextElementSibling
            }
          };
        }
        return true;
      }
      return false;
    }
    startContainerDrag(event) {
      const me = this, { context, floatRootOwner } = me, { element: dragElement } = context, clonedNode = dragElement.cloneNode(true), outerWidgetEl = floatRootOwner == null ? void 0 : floatRootOwner.element.closest(".b-outer");
      clonedNode.classList.add(me.dragProxyCls, me.draggingCls);
      ((floatRootOwner == null ? void 0 : floatRootOwner.floatRoot) || DomHelper.getRootElement(dragElement)).appendChild(clonedNode);
      context.dragProxy = clonedNode;
      context.dragging = dragElement;
      dragElement.classList.add(me.dropPlaceholderCls);
      if (floatRootOwner) {
        floatRootOwner.element.classList.add("b-dragging-child");
      }
      if (outerWidgetEl) {
        clonedNode.style.zIndex = floatRootOwner.floatRootMaxZIndex + 1;
      }
    }
    onContainerDragStarted(event) {
      const me = this, { context } = me, { element: dragElement, dragProxy } = context, box = dragElement.getBoundingClientRect();
      if (me.autoSizeClonedTarget) {
        dragProxy.style.width = box.width + "px";
        dragProxy.style.height = box.height + "px";
        DomHelper.setTranslateXY(context.dragProxy, box.left, box.top);
      } else if (me.useCenteredCursor) {
        const proxyBox = dragProxy.getBoundingClientRect();
        Object.assign(context, {
          offsetX: proxyBox.width / 2,
          offsetY: proxyBox.height / 2
        });
        DomHelper.setTranslateXY(dragProxy, event.clientX, event.clientY);
      }
    }
    /**
     * Move the placeholder element into its new position on valid drag.
     * @private
     * @param event
     */
    updateContainerDrag(event) {
      var _a6;
      const me = this, { context } = me, { targetElement } = context;
      if (!context.started || !targetElement) {
        return;
      }
      let containerElement = DomHelper.getAncestor(targetElement, me.containers);
      const willLoseFocus = (_a6 = context.dragging) == null ? void 0 : _a6.contains(DomHelper.getActiveElement(context.dragging));
      if (containerElement && DomHelper.isDescendant(context.element, containerElement)) {
        return;
      }
      if (willLoseFocus) {
        GlobalEvents_default.suspendFocusEvents();
      }
      if (context.valid && (containerElement || me.containers.some((c) => c.contains(targetElement)))) {
        if (!containerElement) {
          containerElement = me.containers.find((c) => c.contains(targetElement)).lastElementChild;
        }
        me.moveNextTo(containerElement, event);
      } else {
        me.revertPosition();
      }
      if (willLoseFocus) {
        GlobalEvents_default.resumeFocusEvents();
      }
      event.preventDefault();
    }
    /**
     * Finalize drag, fire drop.
     * @private
     * @param event
     * @fires drop
     */
    finishContainerDrag(event) {
      const me = this, { context } = me, { dragging, dragProxy, valid, draggedTo, insertBefore, originalPosition } = context;
      if (dragging) {
        context.valid = Boolean(valid && (draggedTo || me.externalDropTargetSelector && event.target.closest(me.externalDropTargetSelector)) && // no drop on self or parent
        (dragging !== insertBefore || originalPosition.parent !== draggedTo));
        context.finalize = (valid2 = context.valid) => {
          if (!valid2 && me.context) {
            me.revertPosition();
          }
          dragging.classList.remove(me.dropPlaceholderCls);
          dragProxy.remove();
          me.reset();
        };
        context.async = false;
        me.trigger("drop", { context, event });
        if (!context.async) {
          context.finalize();
        }
      }
    }
    /**
     * Aborts a drag operation.
     * @private
     * @param {Boolean} [invalid]
     * @param {Object} [event]
     * @param {Boolean} [silent]
     */
    abortContainerDrag(invalid = false, event = null, silent = false) {
      const me = this, { context } = me;
      if (context.dragging) {
        context.dragging.classList.remove(me.dropPlaceholderCls);
        context.dragProxy.remove();
        me.revertPosition();
      }
      if (!silent) {
        me.trigger(invalid ? "drop" : "abort", { context, event });
      }
      me.reset();
    }
    //endregion
    //region Helpers
    /**
     * Updates the drag proxy position.
     * @private
     * @param event
     */
    updateContainerProxy(event) {
      var _a6, _b2;
      const me = this, { context } = me, proxy = context.dragProxy;
      let newX = event.pageX - context.offsetX, newY = event.pageY - context.offsetY;
      if (typeof me.minX === "number") {
        newX = Math.max(me.minX, newX);
      }
      if (typeof me.maxX === "number") {
        newX = Math.min(me.maxX - proxy.offsetWidth, newX);
      }
      if (typeof me.minY === "number") {
        newY = Math.max(me.minY, newY);
      }
      if (typeof me.maxY === "number") {
        newY = Math.min(me.maxY - proxy.offsetHeight, newY);
      }
      if (me.lockX) {
        DomHelper.setTranslateY(proxy, newY);
      } else if (me.lockY) {
        DomHelper.setTranslateX(proxy, newX);
      } else {
        DomHelper.setTranslateXY(proxy, newX, newY);
      }
      let targetElement;
      if (event.type === "touchmove") {
        const touch = event.changedTouches[0];
        targetElement = DomHelper.elementFromPoint(touch.clientX, touch.clientY);
      } else {
        targetElement = event.target;
      }
      context.targetElement = ((_a6 = me.resolveTargetElement) == null ? void 0 : _a6.call(me, targetElement)) || targetElement;
      if (!((_b2 = context.targetElement.nextElementSibling) == null ? void 0 : _b2.matches(`.${me.dropPlaceholderCls}`))) {
        context.insertBefore = context.targetElement.nextElementSibling;
      }
    }
    /**
     * Positions element being dragged in relation to targetElement.
     * @private
     * @param targetElement
     * @param event
     */
    moveNextTo(targetElement, event) {
      const { context } = this, dragElement = context.dragging, parent = targetElement.parentElement;
      if (targetElement !== dragElement) {
        const centerX = Rectangle.from(targetElement).center.x;
        if (this.isRTL && event.pageX > centerX || !this.isRTL && event.pageX < centerX) {
          const prevSibling = targetElement.previousElementSibling;
          parent.insertBefore(dragElement, targetElement);
          context.insertBefore = targetElement;
          if (prevSibling !== dragElement) {
            this.trigger("itemMove", { context, event });
          }
        } else {
          if (targetElement.nextElementSibling) {
            if (targetElement.nextElementSibling !== dragElement) {
              context.insertBefore = targetElement.nextElementSibling;
              parent.insertBefore(dragElement, targetElement.nextElementSibling);
              this.trigger("itemMove", { context, event });
            } else if (!context.insertBefore && dragElement.parentElement.lastElementChild !== dragElement) {
              context.insertBefore = targetElement.nextElementSibling;
            }
          } else {
            parent.appendChild(dragElement);
            this.trigger("itemMove", { context, event });
            context.insertBefore = null;
          }
        }
        context.draggedTo = parent;
      }
    }
    /**
     * Moves element being dragged back to its original position.
     * @private
     */
    revertPosition() {
      const { context } = this, { dragging, originalPosition } = context, { parent, next } = originalPosition, isNoop = originalPosition.parent === dragging.parent && originalPosition.prev === dragging.previousElementSibling && originalPosition.next === dragging.nextElementSibling;
      if (!isNoop) {
        if (next) {
          parent.insertBefore(dragging, next);
        } else {
          parent.appendChild(dragging);
        }
        this.trigger("itemMove", { context });
      }
      context.draggedTo = null;
    }
    //endregion
  }, __publicField(_a5, "$name", "DragHelperContainer"), _a5;
};

// ../Core/lib/Core/helper/mixin/DragHelperTranslate.js
var noScroll = { pageXOffset: 0, pageYOffset: 0 };
var DragHelperTranslate_default = (Target) => {
  var _a5;
  return _a5 = class extends Delayable_default(Target || Base) {
    static get configurable() {
      return {
        positioning: null,
        // Private config that disables updating elements position, for when data is live updated during drag,
        // leading to element being redrawn
        skipUpdatingElement: null
      };
    }
    //region Init
    /**
     * Initialize translation drag mode.
     * @private
     */
    initTranslateDrag() {
      const me = this;
      if (!me.isElementDraggable && me.targetSelector) {
        me.isElementDraggable = (element) => element.closest(me.targetSelector);
      }
    }
    //endregion
    //region Grab, update, finish
    /**
     * Grab an element which can be moved using translation.
     * @private
     * @param event
     * @returns {Boolean}
     */
    grabTranslateDrag(event) {
      const element = this.getTarget(event);
      if (element) {
        this.context = {
          valid: true,
          element,
          position: DomHelper.getStyleValue(element, "position"),
          startPageX: event.pageX,
          startPageY: event.pageY,
          startClientX: event.clientX,
          startClientY: event.clientY
        };
        return true;
      }
      return false;
    }
    getTarget(event) {
      const { targetSelector, target } = this;
      if (targetSelector) {
        return event.target.closest(targetSelector);
      }
      const element = target.isWidget ? target.element : target;
      return element.contains(event.target) ? element : null;
    }
    getX(element) {
      if (this.positioning === "absolute") {
        return parseFloat(element.style.left, 10);
      }
      if (this.positioning === "inset") {
        return parseFloat(getComputedStyle(element).insetInlineStart, 10);
      }
      return DomHelper.getTranslateX(element);
    }
    getY(element) {
      if (this.positioning === "absolute") {
        return parseFloat(element.style.top, 10);
      }
      if (this.positioning === "inset") {
        return parseFloat(getComputedStyle(element).top, 10);
      }
      return DomHelper.getTranslateY(element);
    }
    getXY(element) {
      if (this.positioning === "absolute") {
        return [element.offsetLeft, element.offsetTop];
      }
      if (this.positioning === "inset") {
        const style = getComputedStyle(element);
        return [parseFloat(style.insetInlineStart, 10), parseFloat(style.top, 10)];
      }
      return DomHelper.getTranslateXY(element);
    }
    setXY(element, x, y) {
      if (this.skipUpdatingElement) {
        return;
      }
      if (this.positioning === "absolute") {
        element.style.left = x + "px";
        element.style.top = y + "px";
      } else if (this.positioning === "inset") {
        element.style.insetInlineStart = x + "px";
        element.style.top = y + "px";
      } else {
        DomHelper.setTranslateXY(element, x, y);
      }
    }
    /**
     * Start translating, called on first mouse move after dragging
     * @private
     * @param event
     */
    startTranslateDrag(event) {
      const me = this, { context, outerElement, proxySelector } = me, dragWithin = me.dragWithin = me.dragWithin || me.cloneTarget && document.body;
      let element = context.dragProxy || context.element;
      const grabbed = element, grabbedParent = element.parentElement;
      if (me.cloneTarget) {
        const elementToClone = proxySelector ? element.querySelector(proxySelector) : element;
        let { width, height, x: proxyX, y: proxyY } = Rectangle.from(elementToClone, Rectangle.outer(dragWithin));
        if (me.isRTL && element.style.insetInlineStart) {
          proxyX = Rectangle.outer(dragWithin).right - Rectangle.from(element).right;
        }
        element = me.createProxy(element);
        let x = proxyX, y = proxyY;
        if (me.autoSizeClonedTarget) {
          element.style.width = `${width}px`;
          element.style.height = `${height}px`;
        }
        element.classList.add(me.dragProxyCls, me.draggingCls);
        element.classList.remove("b-hover", "b-selected", "b-focused");
        dragWithin.appendChild(element);
        if (!me.autoSizeClonedTarget || proxySelector) {
          const proxyRect = element.getBoundingClientRect(), { x: dragWithinX, y: dragWithinY } = dragWithin.getBoundingClientRect(), localX = event.clientX - dragWithinX, localY = event.clientY - dragWithinY + (dragWithin !== document.body ? document.body.getBoundingClientRect().y : 0);
          x = localX - proxyRect.width / 2;
          y = localY - proxyRect.height / 2;
          context.startPageX = event.pageX;
          context.startPageY = event.pageY;
        }
        me.setXY(element, x, y);
        grabbed.classList.add("b-drag-original");
        if (me.hideOriginalElement) {
          grabbed.classList.add("b-hidden");
        }
      }
      element.classList.add(me.draggingCls);
      const elementStartX = me.getX(element), elementStartY = me.getY(element);
      Object.assign(context, {
        // The element which we're moving, could be a cloned version of grabbed, or the grabbed element itself
        element,
        // The original element upon which the mousedown event triggered a drag operation
        grabbed,
        // The parent of the original element where the pointerdown was detected - to be able to restore after an invalid drop
        grabbedParent,
        // The next sibling of the original element where the pointerdown was detected - to be able to restore after an invalid drop
        grabbedNextSibling: element.nextElementSibling,
        // Elements position within parent element
        elementStartX,
        elementStartY,
        // The inset mode currently only supports dragging within the offsetParent element, to ensure that inset
        // coord can be accurately read without walking
        elementX: me.positioning === "inset" ? elementStartX : DomHelper.getOffsetX(element, dragWithin || outerElement),
        elementY: me.positioning === "inset" ? elementStartY : DomHelper.getOffsetY(element, dragWithin || outerElement),
        scrollX: 0,
        scrollY: 0,
        scrollManagerElementContainsDragProxy: !me.cloneTarget || dragWithin === outerElement
      });
      if (dragWithin) {
        context.parentElement = element.parentElement;
        if (dragWithin !== element.parentElement) {
          dragWithin.appendChild(element);
        }
        me.updateTranslateProxy(event);
      }
    }
    // When drag has started, create proxy versions (if applicable) and store original positions of all related elements
    // to be able to animate back to these positions in case of an aborted drag
    onTranslateDragStarted() {
      const me = this, { context } = me;
      let { relatedElements } = context;
      if (me.unifiedProxy) {
        context.element.classList.add("b-drag-main", "b-drag-unified-proxy");
      }
      if ((relatedElements == null ? void 0 : relatedElements.length) > 0) {
        context.relatedElStartPos = [];
        context.relatedElDragFromPos = [];
        const { proxySelector } = me;
        let [elementStartX, elementStartY] = [context.elementStartX, context.elementStartY];
        context.originalRelatedElements = relatedElements;
        relatedElements = context.relatedElements = relatedElements.map((relatedEl, i) => {
          const proxyTemplateElement = proxySelector ? relatedEl.querySelector(proxySelector) : relatedEl, { x, y, width, height } = Rectangle.from(proxyTemplateElement, me.dragWithin), relatedElementToDrag = me.cloneTarget ? me.createProxy(relatedEl) : relatedEl;
          relatedElementToDrag.classList.add(me.draggingCls);
          relatedElementToDrag.classList.remove("b-hover", "b-selected", "b-focused");
          if (me.cloneTarget) {
            me.setXY(relatedElementToDrag, x, y);
            me.dragWithin.appendChild(relatedElementToDrag);
            relatedElementToDrag.classList.add(me.dragProxyCls);
            if (me.autoSizeClonedTarget) {
              relatedElementToDrag.style.width = `${width}px`;
              relatedElementToDrag.style.height = `${height}px`;
            }
            if (me.hideOriginalElement) {
              relatedEl.classList.add("b-hidden");
            }
            relatedEl.classList.add("b-drag-original");
          }
          context.relatedElStartPos[i] = context.relatedElDragFromPos[i] = me.getXY(relatedElementToDrag);
          if (me.unifiedProxy) {
            relatedElementToDrag.classList.add("b-drag-unified-animation", "b-drag-unified-proxy");
            elementStartX += me.unifiedOffset;
            elementStartY += me.unifiedOffset;
            me.setXY(relatedElementToDrag, elementStartX, elementStartY);
            context.relatedElDragFromPos[i] = [elementStartX, elementStartY];
            relatedElementToDrag.style.zIndex = 100 - i;
          }
          return relatedElementToDrag;
        });
        if (me.unifiedProxy && relatedElements && relatedElements.length > 0) {
          EventHelper.onTransitionEnd({
            element: relatedElements[0],
            property: "transform",
            handler() {
              relatedElements.forEach((el) => el.classList.remove("b-drag-unified-animation"));
            },
            thisObj: me,
            once: true
          });
        }
      }
    }
    /**
     * Limit translation to outer bounds and specified constraints
     * @private
     * @param element
     * @param x
     * @param y
     * @returns {{constrainedX: *, constrainedY: *}}
     */
    applyConstraints(element, x, y) {
      const me = this, { constrain: constrain2, dragWithin } = me, { pageXOffset, pageYOffset } = dragWithin === document.body ? globalThis : noScroll;
      if (dragWithin && constrain2) {
        const newRect = new Rectangle(x, y, element.offsetWidth, element.offsetHeight), constrainRect = Rectangle.from(dragWithin);
        if (me.context.position !== "fixed") {
          constrainRect.height = dragWithin.scrollHeight;
          constrainRect.width = dragWithin.scrollWidth;
          constrainRect.moveTo(0, 0);
        }
        ({ x, y } = newRect.constrainTo(constrainRect));
      }
      if (typeof me.minX === "number") {
        x = Math.max(me.minX + pageXOffset, x);
      }
      if (typeof me.maxX === "number") {
        x = Math.min(me.maxX + pageXOffset, x);
      }
      if (typeof me.minY === "number") {
        y = Math.max(me.minY + pageYOffset, y);
      }
      if (typeof me.maxY === "number") {
        y = Math.min(me.maxY + pageYOffset, y);
      }
      return { constrainedX: x, constrainedY: y };
    }
    /**
     * Update elements translation on mouse move.
     * @private
     * @param {MouseEvent} event
     * @param {Object} scrollManagerConfig
     */
    updateTranslateProxy(event, scrollManagerConfig) {
      const me = this, { lockX, lockY, context } = me, element = context.dragProxy || context.element, { relatedElements, relatedElDragFromPos } = context, rtlInsetFlip = me.isRTL && me.positioning === "inset" ? -1 : 1;
      if (context.scrollManagerElementContainsDragProxy && scrollManagerConfig) {
        context.scrollX = scrollManagerConfig.getRelativeLeftScroll(element);
        context.scrollY = scrollManagerConfig.getRelativeTopScroll(element);
      }
      context.pageX = event.pageX;
      context.pageY = event.pageY;
      context.clientX = event.clientX;
      context.clientY = event.clientY;
      let newX = context.elementStartX + (event.pageX - context.startPageX + context.scrollX) * rtlInsetFlip, newY = context.elementStartY + event.pageY - context.startPageY + context.scrollY;
      if (me.snapCoordinates) {
        const snapped = me.snapCoordinates({ element, newX, newY });
        newX = snapped.x;
        newY = snapped.y;
      }
      const { constrainedX, constrainedY } = me.applyConstraints(element, newX, newY);
      if (context.started || constrainedX !== newX || constrainedY !== newY) {
        me.setXY(element, lockX ? void 0 : constrainedX, lockY ? void 0 : constrainedY);
      }
      if (relatedElements) {
        const deltaX = lockX ? 0 : constrainedX - context.elementStartX, deltaY = lockY ? 0 : constrainedY - context.elementStartY;
        relatedElements.forEach((r, i) => {
          const [x, y] = relatedElDragFromPos[i];
          me.setXY(r, x + deltaX, y + deltaY);
        });
      }
      context.newX = constrainedX;
      context.newY = constrainedY;
    }
    /**
     * Finalize drag, fire drop.
     * @private
     * @param event
     * @fires drop
     */
    async finishTranslateDrag(event) {
      const me = this, context = me.context, { target } = event, xChanged = !me.lockX && Math.round(context.newX) !== Math.round(context.elementStartX), yChanged = !me.lockY && Math.round(context.newY) !== Math.round(context.elementStartY), element = context.dragProxy || context.element, { relatedElements } = context;
      if (!me.ignoreSamePositionDrop || xChanged || yChanged) {
        if (context.valid === false) {
          await me.abortTranslateDrag(true, event);
        } else {
          const targetRect = !me.allowDropOutside && Rectangle.from(me.dragWithin || me.outerElement);
          if (targetRect && (typeof me.minX !== "number" && me.minX !== true && event.pageX < targetRect.left || typeof me.maxX !== "number" && me.maxX !== true && event.pageX > targetRect.right || typeof me.minY !== "number" && me.minY !== true && event.pageY < targetRect.top || typeof me.maxY !== "number" && me.maxY !== true && event.pageY > targetRect.bottom)) {
            await me.abortTranslateDrag(true, event);
          } else {
            context.finalize = async (valid = context.valid) => {
              if (context.finalized) {
                console.warn("DragHelper: Finalizing already finalized drag");
                return;
              }
              context.finalized = true;
              if (!valid && me.context) {
                await me.abortTranslateDrag(true, null, true);
              }
              if (!me.isDestroyed) {
                me.trigger("dropFinalized", { context, event, target });
                me.reset();
              }
              if (!me.cloneTarget && element.parentElement !== context.grabbedParent) {
                [element, ...relatedElements || []].forEach((el) => el.style.transform = "");
              }
            };
            context.async = false;
            await me.trigger("drop", { context, event, target });
            if (!context.async) {
              await context.finalize();
            }
          }
        }
      } else {
        me.abortTranslateDrag(false, event);
      }
    }
    /**
     * Abort translation
     * @private
     * @param invalid
     * @fires abort
     */
    async abortTranslateDrag(invalid = false, event = null, silent = false) {
      var _a6, _b2, _c2;
      const me = this, {
        cloneTarget,
        context,
        proxySelector,
        dragWithin,
        draggingCls
      } = me, { relatedElements, relatedElStartPos, grabbed } = context, element = context.dragProxy || context.element;
      context.valid = false;
      (_a6 = me.scrollManager) == null ? void 0 : _a6.stopMonitoring();
      if (context.aborted) {
        console.warn("DragHelper: Aborting already aborted drag");
        return;
      }
      let { elementStartX, elementStartY } = context;
      const proxyMoved = elementStartX !== me.getX(element) || elementStartY !== me.getY(element);
      if (element && context.started) {
        if (!cloneTarget && dragWithin && dragWithin !== context.grabbedParent) {
          context.grabbedParent.insertBefore(element, context.grabbedNextSibling);
        }
        if (cloneTarget) {
          if (proxySelector) {
            const animateTo = grabbed.querySelector(proxySelector) || grabbed, { x, y } = Rectangle.from(animateTo);
            elementStartX = x;
            elementStartY = y;
          }
        }
        element.classList.add("b-aborting");
        me.setXY(element, elementStartX, elementStartY);
        relatedElements == null ? void 0 : relatedElements.forEach((element2, i) => {
          element2.classList.remove(draggingCls);
          element2.classList.add("b-aborting");
          me.setXY(element2, relatedElStartPos[i][0], relatedElStartPos[i][1]);
        });
        if (!silent) {
          me.trigger(invalid ? "drop" : "abort", { context, event });
        }
        if (element.isConnected && !me.isDestroying && proxyMoved) {
          const transitionProperty = me.positioning === "inset" ? "inset" : "transform";
          if ((_b2 = element.getAnimations) == null ? void 0 : _b2.call(element).length) {
            await EventHelper.waitForTransitionEnd({
              element,
              property: DomHelper.getPropertyTransitionDuration(element, transitionProperty) ? transitionProperty : "all",
              thisObj: me,
              once: true,
              timeout: 300,
              runOnDestroy: true
            });
          }
        }
        if (!me.isDestroyed) {
          me.trigger("abortFinalized", { context, event });
        }
      }
      if ((_c2 = me.context) == null ? void 0 : _c2.started) {
        me.reset();
      }
    }
    // Restore state of all mutated elements
    cleanUp() {
      const me = this, { context, cloneTarget, draggingCls, dragProxyCls } = me, element = context.dragProxy || context.element, { relatedElements, originalRelatedElements, grabbed } = context, removeClonedProxies = cloneTarget && (me.removeProxyAfterDrop || !context.valid), cssClassesToRemove = [draggingCls, "b-aborting", dragProxyCls, "b-drag-main", "b-drag-unified-proxy"];
      element.classList.remove(...cssClassesToRemove);
      if (removeClonedProxies) {
        element.remove();
      }
      relatedElements == null ? void 0 : relatedElements.forEach((element2) => {
        if (removeClonedProxies) {
          element2.remove();
        } else {
          element2.classList.remove(...cssClassesToRemove);
        }
      });
      grabbed.classList.remove("b-drag-original", "b-hidden");
      originalRelatedElements == null ? void 0 : originalRelatedElements.forEach((element2) => element2.classList.remove("b-hidden", "b-drag-original"));
    }
    //endregion
  }, __publicField(_a5, "$name", "DragHelperTranslate"), _a5;
};

// ../Core/lib/Core/helper/DragHelper.js
var rootElementListeners = {
  move: "onMouseMove",
  up: "onMouseUp",
  docclick: "onDocumentClick",
  touchstart: "onTouchStart",
  touchmove: "onTouchMove",
  touchend: "onTouchEnd",
  keydown: "onKeyDown",
  keyup: "onKeyDownUp"
};
var DragHelper = class _DragHelper extends Base.mixin(Events_default, DragHelperContainer_default, DragHelperTranslate_default) {
  //region Config
  static get defaultConfig() {
    return {
      /**
       * Drag proxy CSS class
       * @config {String}
       * @default
       * @private
       */
      dragProxyCls: "b-drag-proxy",
      /**
       * CSS class added when drag is invalid
       * @config {String}
       * @default
       */
      invalidCls: "b-drag-invalid",
      /**
       * CSS class added to the source element in Container drag
       * @config {String}
       * @default
       * @private
       */
      draggingCls: "b-dragging",
      /**
       * CSS class added to the source element in Container drag
       * @config {String}
       * @default
       * @private
       */
      dropPlaceholderCls: "b-drop-placeholder",
      /**
       * The amount of pixels to move mouse before it counts as a drag operation
       * @config {Number}
       * @default
       */
      dragThreshold: 5,
      /**
       * The outer element where the drag helper will operate (attach events to it and use as outer limit when looking for ancestors)
       * @config {HTMLElement}
       * @default
       */
      outerElement: document.body,
      /**
       * Outer element that limits where element can be dragged
       * @config {HTMLElement}
       */
      dragWithin: null,
      /**
       * Set to `true` to stack any related dragged elements below the main drag proxy element. Only applicable when
       * using translate {@link #config-mode} with {@link #config-cloneTarget}
       * @config {Boolean}
       */
      unifiedProxy: null,
      monitoringConfig: null,
      /**
       * Constrain translate drag to dragWithin elements bounds (set to `false` to allow it to "overlap" edges)
       * @config {Boolean}
       * @default
       */
      constrain: true,
      /**
       * Smallest allowed x when dragging horizontally.
       * @config {Number}
       */
      minX: null,
      /**
       * Largest allowed x when dragging horizontally.
       * @config {Number}
       */
      maxX: null,
      /**
       * Smallest allowed y when dragging horizontally.
       * @config {Number}
       */
      minY: null,
      /**
       * Largest allowed y when dragging horizontally.
       * @config {Number}
       */
      maxY: null,
      /**
       * Enabled dragging, specify mode:
       * <table>
       * <tr><td>container<td>Allows reordering elements within one and/or between multiple containers
       * <tr><td>translateXY<td>Allows dragging within a parent container
       * </table>
       * @config {'container'|'translateXY'}
       * @default
       */
      mode: "translateXY",
      /**
       * A function that determines if dragging an element is allowed. Gets called with the element as argument,
       * return `true` to allow dragging or `false` to prevent.
       * @config {Function}
       * @param {HTMLElement} element
       * @returns {Boolean}
       */
      isElementDraggable: null,
      /**
       * A CSS selector used to target draggable elements. See also {@link #config-target}.
       * @config {String}
       */
      targetSelector: null,
      /**
       * A Widget or HTML element to drag. See also {@link #config-targetSelector}.
       * @config {HTMLElement|Core.widget.Widget}
       */
      target: null,
      /**
       * A CSS selector used to determine if a drop is allowed at the current position.
       * @config {String}
       */
      dropTargetSelector: null,
      /**
       * A CSS selector added to each drop target element while dragging.
       * @config {String}
       */
      dropTargetCls: null,
      /**
       * A CSS selector used to target a child element of the mouse down element, to use as the drag proxy element.
       * Applies to translate {@link #config-mode mode} when using {@link #config-cloneTarget}.
       * @config {String}
       */
      proxySelector: null,
      /**
       * Set to `true` to clone the dragged target, and not move the actual target DOM node.
       * @config {Boolean}
       * @default
       */
      cloneTarget: false,
      /**
       * Set to `false` to not apply width/height of cloned drag proxy elements.
       * @config {Boolean}
       * @default
       */
      autoSizeClonedTarget: true,
      /**
       * Set to `true` to hide the original element while dragging (applicable when `cloneTarget` is true).
       * @config {Boolean}
       * @default
       */
      hideOriginalElement: false,
      /**
       * Containers whose elements can be rearranged (and moved between the containers). Used when
       * mode is set to "container".
       * @config {HTMLElement[]}
       */
      containers: null,
      /**
       * A CSS selector used to exclude elements when using container mode
       * @config {String}
       */
      ignoreSelector: null,
      startEvent: null,
      /**
       * Configure as `true` to disallow dragging in the `X` axis. The dragged element will only move vertically.
       * @config {Boolean}
       * @default
       */
      lockX: false,
      /**
       * Configure as `true` to disallow dragging in the `Y` axis. The dragged element will only move horizontally.
       * @config {Boolean}
       * @default
       */
      lockY: false,
      /**
       * The amount of milliseconds to wait after a touchstart, before a drag gesture will be allowed to start.
       * @config {Number}
       * @default
       */
      touchStartDelay: 200,
      /**
       * Scroll manager of the target. If specified, scrolling while dragging is supported.
       * @config {Core.util.ScrollManager}
       */
      scrollManager: null,
      /**
       * A method provided to snap coordinates to fixed points as you drag
       * @config {Function}
       * @internal
       */
      snapCoordinates: null,
      /**
       * When using {@link #config-unifiedProxy}, use this amount of pixels to offset each extra element when dragging multiple items
       * @config {Number}
       * @default
       */
      unifiedOffset: 5,
      /**
       * Configure as `false` to take ownership of the proxy element after a valid drop (advanced usage).
       * @config {Boolean}
       * @default
       */
      removeProxyAfterDrop: true,
      clickSwallowDuration: 50,
      ignoreSamePositionDrop: true,
      // true to allow drops outside the dragWithin element
      allowDropOutside: null,
      // for container mode
      floatRootOwner: null,
      mouseMoveListenerElement: document,
      externalDropTargetSelector: null,
      testConfig: {
        clickSwallowDuration: 50,
        touchStartDelay: 100
      },
      rtlSource: null,
      /**
       * Creates the proxy element to be dragged, when using {@link #config-cloneTarget}. Clones the original element by default.
       * Provide your custom {@link #function-createProxy} function to be used for creating drag proxy.
       * @param {HTMLElement} element The element from which the drag operation originated
       * @config {Function}
       * @returns {HTMLElement}
       */
      createProxy: null,
      resolveTargetElement: null,
      disabled: false
    };
  }
  //endregion
  //region Events
  /**
   * Fired before dragging starts, return `false` to prevent the drag operation.
   * @preventable
   * @event beforeDragStart
   * @param {Core.helper.DragHelper} source
   * @param {Object} context
   * @param {HTMLElement} context.element The original element upon which the mousedown event triggered a drag operation
   * @param {MouseEvent|TouchEvent} event
   */
  /**
   * Fired when dragging starts. The event includes a `context` object. If you want to drag additional elements you can
   * provide these as an array of elements assigned to the `relatedElements` property of the context object.
   * @event dragStart
   * @param {Core.helper.DragHelper} source
   * @param {Object} context
   * @param {HTMLElement} context.element The element which we're moving, could be a cloned version of grabbed, or the grabbed element itself
   * @param {HTMLElement} context.grabbed The original element upon which the mousedown event triggered a drag operation
   * @param {HTMLElement[]} context.relatedElements Array of extra elements to include in the drag.
   * @param {MouseEvent|TouchEvent} event
   */
  /**
   * Fired while dragging, you can signal that the drop is valid or invalid by setting `context.valid = false;`
   * @event drag
   * @param {Core.helper.DragHelper} source
   * @param {Object} context
   * @param {HTMLElement} context.element The element which we are moving, could be a cloned version of grabbed, or the grabbed element itself
   * @param {HTMLElement} context.target The target element below the cursor
   * @param {HTMLElement} context.grabbed The original element upon which the mousedown event triggered a drag operation
   * @param {Number} context.newX The new X page coordinate of the dragged element
   * @param {Number} context.newY The new Y page coordinate of the dragged element
   * @param {HTMLElement[]} context.relatedElements An array of extra elements dragged with the main dragged element
   * @param {Boolean} context.valid Set this to true or false to indicate whether the drop position is valid.
   * @param {MouseEvent} event
   */
  /**
   * Fired after a drop at an invalid position
   * @event abort
   * @param {Core.helper.DragHelper} source
   * @param {Object} context
   * @param {HTMLElement} context.element The element which we are moving, could be a cloned version of grabbed, or the grabbed element itself
   * @param {HTMLElement} context.target The target element below the cursor
   * @param {HTMLElement} context.grabbed The original element upon which the mousedown event triggered a drag operation
   * @param {HTMLElement[]} context.relatedElements An array of extra elements dragged with the main dragged element
   * @param {MouseEvent} event
   */
  /**
   * Fires after {@link #event-abort} and after drag proxy has animated back to its original position
   * @private
   * @event abortFinalized
   * @param {Core.helper.DragHelper} source
   * @param {Object} context
   * @param {HTMLElement} context.element The element which we are moving, could be a cloned version of grabbed, or the grabbed element itself
   * @param {HTMLElement} context.target The target element below the cursor
   * @param {HTMLElement} context.grabbed The original element upon which the mousedown event triggered a drag operation
   * @param {MouseEvent} event
   */
  //endregion
  //region Init
  /**
   * Initializes a new DragHelper.
   * @param {DragHelperConfig} config Configuration object, accepts options specified under Configs above
   *
   * ```javascript
   * new DragHelper({
   *   containers: [div1, div2],
   *   isElementDraggable: element => element.className.contains('handle'),
   *   outerElement: topParent,
   *   listeners: {
   *     drop: onDrop,
   *     thisObj: this
   *   }
   * });
   * ```
   *
   * @function constructor
   */
  construct(config) {
    const me = this;
    super.construct(config);
    me.initListeners();
    if (me.isContainerDrag) {
      me.initContainerDrag();
    } else {
      me.initTranslateDrag();
    }
    me.onScrollManagerScrollCallback = me.onScrollManagerScrollCallback.bind(me);
  }
  doDestroy() {
    this.reset(true);
    super.doDestroy();
  }
  /**
   * Initialize listener
   * @private
   */
  initListeners() {
    const me = this, { outerElement } = me, dragStartListeners = {
      element: outerElement,
      pointerdown: "onPointerDown",
      thisObj: me
    };
    me.mouseMoveListenerElement = me.getMouseMoveListenerTarget(outerElement);
    EventHelper.on(dragStartListeners);
  }
  // Salesforce hook: we override this method to move listener from the body (which is default root node) to element
  // inside of LWC
  getMouseMoveListenerTarget(element) {
    const root = element.getRootNode();
    let result = this.mouseMoveListenerElement;
    if (root.nodeType === Node.DOCUMENT_FRAGMENT_NODE && root.mode === "closed") {
      result = element.closest(".b-outer") || result;
    }
    return result;
  }
  get isRTL() {
    var _a5;
    return Boolean((_a5 = this.rtlSource) == null ? void 0 : _a5.rtl);
  }
  //endregion
  //region Events
  /**
   * Fires after drop. For valid drops, it exposes `context.async` which you can set to `true` to signal that additional
   * processing is needed before finalizing the drop (such as showing some dialog). When that operation is done, call
   * `context.finalize(true/false)` with a boolean that determines the outcome of the drop.
   *
   * You can signal that the drop is valid or invalid by setting `context.valid = false;`
   *
   * For translate type drags with {@link #config-cloneTarget}, you can also set `transitionTo` if you want to animate
   * the dragged proxy to a position before finalizing the operation. See class intro text for example usage.
   *
   * @event drop
   * @param {Core.helper.DragHelper} source
   * @param {Object} context
   * @param {HTMLElement} context.element The element which we are moving, could be a cloned version of grabbed, or the grabbed element itself
   * @param {HTMLElement} context.target The target element below the cursor
   * @param {HTMLElement} context.grabbed The original element upon which the mousedown event triggered a drag operation
   * @param {HTMLElement[]} context.relatedElements An array of extra elements dragged with the main dragged element
   * @param {Boolean} context.valid true if the drop position is valid
   */
  /**
   * Fires after {@link #event-drop} and after drag proxy has animated to its final position (if setting `transitionTo`
   * on the drag context object).
   * @private
   * @event dropFinalized
   * @param {Core.helper.DragHelper} source
   * @param {Object} context
   * @param {HTMLElement} context.element The element which we are moving, could be a cloned version of grabbed, or the grabbed element itself
   * @param {HTMLElement} context.target The target element below the cursor
   * @param {HTMLElement} context.grabbed The original element upon which the mousedown event triggered a drag operation
   */
  onPointerDown(event) {
    const me = this;
    if (
      // Left button or touch allowed
      event.button !== 0 || // If a drag is ongoing already, finalize it and don't proceed with new drag (happens if pointerup happened
      // when current window wasn't focused - tab switch or window switch). Also handles the edge case of trying to
      // start a new drag while previous is awaiting finalization, in which case it just bails out.
      me.context || me.disabled || // Check that element is draggable
      me.isElementDraggable && !me.isElementDraggable(event.target, event)
    ) {
      return;
    }
    me.startEvent = event;
    const handled = me.isContainerDrag ? me.grabContainerDrag(event) : me.grabTranslateDrag(event);
    if (handled) {
      const element = globalThis;
      me.blurDetacher = EventHelper.on({
        element,
        blur: me.onWindowBlur,
        thisObj: me
      });
      const dragListeners = {
        element: me.mouseMoveListenerElement,
        thisObj: me,
        capture: true,
        keydown: rootElementListeners.keydown,
        keyup: rootElementListeners.keyup
      };
      if (event.pointerType === "touch") {
        me.touchStartTimer = me.setTimeout(() => me.touchStartTimer = null, me.touchStartDelay, "touchStartDelay");
        dragListeners.touchmove = {
          handler: rootElementListeners.touchmove,
          passive: false
          // We need to be able to preventDefault on the touchmove
        };
        dragListeners.touchend = dragListeners.pointerup = rootElementListeners.touchend;
      } else {
        dragListeners.pointermove = rootElementListeners.move;
        dragListeners.pointerup = rootElementListeners.up;
      }
      me.dragListenersDetacher = EventHelper.on(dragListeners);
      if (me.dragWithin && me.dragWithin !== me.outerElement && me.outerElement.contains(me.dragWithin)) {
        const box = Rectangle.from(me.dragWithin, me.outerElement);
        me.minY = box.top;
        me.maxY = box.bottom;
        me.minX = box.left;
        me.maxX = box.right;
      }
    }
  }
  async internalMove(event) {
    var _a5, _b2, _c2, _d;
    if (event.scrollInitiated) {
      return;
    }
    const me = this, { context } = me, distance = EventHelper.getDistanceBetween(me.startEvent, event), abortTouchDrag = me.touchStartTimer && distance > me.dragThreshold;
    if (abortTouchDrag) {
      me.abort(true);
      return;
    }
    if (!me.touchStartTimer && (context == null ? void 0 : context.element) && (context.started || distance >= me.dragThreshold) && // Ignore text nodes
    ((_a5 = event.target) == null ? void 0 : _a5.nodeType) === Node.ELEMENT_NODE) {
      if (!context.started) {
        const xy1 = EventHelper.getXY(me.startEvent), xy2 = EventHelper.getXY(event), initialDirection = Math.abs(xy1[0] - xy2[0]) >= Math.abs(xy1[1] - xy2[1]) ? "horizontal" : "vertical";
        if (!context.beforeDragStartFired && me.trigger("beforeDragStart", { context, event, initialDirection }) === false) {
          return me.abort();
        }
        context.beforeDragStartFired = true;
        if (Math.abs(EventHelper.getDistanceBetween(me.startEvent, event, { x: me.lockY, y: me.lockX })) < me.dragThreshold) {
          return;
        }
        if (me.isContainerDrag) {
          me.startContainerDrag(event);
        } else {
          me.startTranslateDrag(event);
        }
        if ((_c2 = (_b2 = context.dragProxy) == null ? void 0 : _b2.closest) == null ? void 0 : _c2.call(_b2, ".b-float-root")) {
          context.dragProxy.style.fontSize = DomHelper.getStyleValue(context.element, "fontSize");
        }
        context.started = _DragHelper.dragging = true;
        (_d = me.scrollManager) == null ? void 0 : _d.startMonitoring(ObjectHelper.merge({
          scrollables: [
            {
              element: me.dragWithin || me.outerElement
            }
          ],
          callback: me.onScrollManagerScrollCallback
        }, me.monitoringConfig));
        context.outermostEl = DomHelper.getOutermostElement(event.target);
        context.outermostEl.classList.add("b-draghelper-active");
        if (me.dropTargetSelector && me.dropTargetCls) {
          DomHelper.getRootElement(me.outerElement).querySelectorAll(me.dropTargetSelector).forEach(
            (el) => el.classList.add(me.dropTargetCls)
          );
        }
        const result = me.trigger("dragStart", { context, event, initialDirection });
        if (ObjectHelper.isPromise(result)) {
          await result;
        }
        me.onKeyDownUp(event);
        context.moveUnblocked = true;
        if (me.isContainerDrag) {
          me.onContainerDragStarted(event);
        } else {
          me.onTranslateDragStarted(event);
        }
        me.trigger("afterDragStart", { context, event, initialDirection });
      }
      if (context.moveUnblocked) {
        if (me._cachedMouseEvent) {
          me.update(event);
          me.update(me._cachedMouseEvent);
          delete me._cachedMouseEvent;
        } else {
          me.update(event);
        }
      } else {
        me._cachedMouseEvent = event;
      }
    }
  }
  onScrollManagerScrollCallback(config) {
    var _a5;
    const { lastMouseMoveEvent } = this;
    if (((_a5 = this.context) == null ? void 0 : _a5.element) && lastMouseMoveEvent) {
      lastMouseMoveEvent.isScroll = true;
      this.update(lastMouseMoveEvent, config);
    }
  }
  onTouchMove(event) {
    var _a5;
    if (((_a5 = event.changedTouches) == null ? void 0 : _a5.length) === 1) {
      if (this.context && !this.touchStartTimer) {
        event.preventDefault();
      }
      this.internalMove(event);
    }
  }
  /**
   * Move drag element with mouse.
   * @param event
   * @fires beforeDragStart
   * @fires dragStart
   * @private
   */
  onMouseMove(event) {
    this.internalMove(event);
  }
  /**
   * Updates drag, called when an element is grabbed and mouse moves
   * @private
   * @fires drag
   */
  update(event, scrollManagerConfig) {
    const me = this, scrollingPageElement = document.scrollingElement || document.body, { context } = me;
    let target = me.getMouseMoveEventTarget(event);
    if (event.type === "touchmove") {
      const touch = event.changedTouches[0], root = DomHelper.getRootElement(event.target);
      target = root.getRootNode().elementFromPoint(touch.clientX + scrollingPageElement.scrollLeft, touch.clientY + scrollingPageElement.scrollTop);
    }
    context.target = target;
    let internallyValid = me.allowDropOutside || !me.dragWithin || me.dragWithin.contains(event.target);
    if (internallyValid && me.dropTargetSelector) {
      internallyValid = internallyValid && Boolean(target == null ? void 0 : target.closest(me.dropTargetSelector));
    }
    if (me.isContainerDrag) {
      me.updateContainerProxy(event, scrollManagerConfig);
    } else {
      me.updateTranslateProxy(event, scrollManagerConfig);
    }
    context.valid = internallyValid;
    me.trigger("drag", { context, event });
    if (me.isContainerDrag) {
      me.updateContainerDrag(event, scrollManagerConfig);
    }
    context.valid = context.valid && internallyValid;
    for (const element of me.draggedElements) {
      element.classList.toggle(me.invalidCls, !context.valid);
    }
    if (event) {
      me.lastMouseMoveEvent = event;
    }
  }
  get draggedElements() {
    var _a5;
    const { context } = this;
    return [context.dragProxy || context.element, ...(_a5 = context.relatedElements) != null ? _a5 : []];
  }
  /**
   * Abort dragging
   * @fires abort
   */
  async abort(silent = false) {
    var _a5, _b2;
    const me = this, { context } = me;
    (_b2 = (_a5 = me.scrollManager) == null ? void 0 : _a5.stopMonitoring) == null ? void 0 : _b2.call(_a5);
    me.removeListeners();
    if ((context == null ? void 0 : context.started) && !context.aborted) {
      context.element.getBoundingClientRect();
      context.valid = false;
      if (me.isContainerDrag) {
        me.abortContainerDrag(void 0, void 0, silent);
      } else {
        me.abortTranslateDrag(void 0, void 0, silent);
      }
      context.aborted = true;
    } else {
      me.reset(true);
    }
  }
  // Empty class implementation. If listeners *are* added, the detacher is added
  // as an instance property. So this is always callable.
  removeListeners() {
    var _a5, _b2;
    (_a5 = this.dragListenersDetacher) == null ? void 0 : _a5.call(this);
    (_b2 = this.blurDetacher) == null ? void 0 : _b2.call(this);
  }
  // Called when a drag operation is completed, or aborted
  // Removes DOM listeners and resets context
  reset(silent) {
    var _a5;
    const me = this, { context } = me;
    if (context == null ? void 0 : context.started) {
      for (const element of me.draggedElements) {
        element.classList.remove(me.invalidCls);
      }
      context.outermostEl.classList.remove("b-draghelper-active");
      if (me.isContainerDrag) {
        context.dragProxy.remove();
        (_a5 = me.floatRootOwner) == null ? void 0 : _a5.element.classList.remove("b-dragging-child");
      } else {
        me.cleanUp();
      }
      if (me.dropTargetSelector && me.dropTargetCls) {
        DomHelper.getRootElement(me.outerElement).querySelectorAll(me.dropTargetSelector).forEach(
          (el) => el.classList.remove(me.dropTargetCls)
        );
      }
    }
    me.removeListeners();
    if (!silent) {
      me.trigger("reset");
    }
    me.context = me.lastMouseMoveEvent = null;
    _DragHelper.dragging = false;
  }
  onTouchEnd(event) {
    DomHelper.getOutermostElement(event.target).classList.remove("b-no-touch-scroll");
    this.onMouseUp(event);
  }
  /**
   * This is a capture listener, only added during drag, which prevents a click gesture
   * propagating from the terminating mouseup gesture
   * @param {MouseEvent} event
   * @private
   */
  onDocumentClick(event) {
    event.stopPropagation();
  }
  /**
   * Drop on mouse up (if dropped on valid target).
   * @param event
   * @private
   */
  onMouseUp(event) {
    var _a5;
    const me = this, { context } = me;
    me.removeListeners();
    if (context) {
      (_a5 = me.scrollManager) == null ? void 0 : _a5.stopMonitoring();
      if (context.started) {
        if (context.moveUnblocked) {
          event.stopPropagation();
          context.finalizing = true;
          if (me.isContainerDrag) {
            me.finishContainerDrag(event);
          } else {
            me.finishTranslateDrag(event);
          }
          EventHelper.on({
            element: document,
            thisObj: me,
            click: rootElementListeners.docclick,
            capture: true,
            expires: me.clickSwallowDuration,
            // In case a click did not ensue, remove the listener
            once: true
          });
        } else {
          me.ion({
            drag() {
              me.onMouseUp(event);
            },
            once: true
          });
        }
      } else {
        me.reset(true);
      }
    }
  }
  /**
   * Cancel on ESC key
   * @param event
   * @private
   */
  onKeyDown(event) {
    var _a5;
    if (((_a5 = this.context) == null ? void 0 : _a5.started) && event.key === "Escape") {
      event.stopImmediatePropagation();
      this.abort();
    }
    this.onKeyDownUp(event);
  }
  onKeyDownUp(event) {
    var _a5;
    (_a5 = this.context) == null ? void 0 : _a5.element.classList.toggle("b-drag-copy", event[BrowserHelper.ctrlKey] || event[BrowserHelper.metaKey]);
  }
  onWindowBlur() {
    if (this.context && !this.context.finalizing) {
      this.abort();
    }
  }
  /**
   * Creates the proxy element to be dragged, when using {@link #config-cloneTarget}. Clones the original element by default.
   * Override it to provide your own custom HTML element structure to be used as the drag proxy.
   * @param {HTMLElement} element The element from which the drag operation originated
   * @returns {HTMLElement}
   */
  createProxy(element) {
    if (this.proxySelector) {
      element = element.querySelector(this.proxySelector) || element;
    }
    const proxy = element.cloneNode(true);
    proxy.removeAttribute("id");
    return proxy;
  }
  //endregion
  get isContainerDrag() {
    return this.mode === "container";
  }
  /**
   * Animated the proxy element to be aligned with the passed element. Returns a Promise which resolves after the
   * DOM transition completes. Only applies to 'translateXY' mode.
   * @param {HTMLElement|Core.helper.util.Rectangle} element The target element or a Rectangle
   * @param {AlignSpec} [alignSpec] An object describing how to the align drag proxy to the target element
   * to offset the aligned widget further from the target. May be configured as -ve to move the aligned widget
   * towards the target - for example producing the effect of the anchor pointer piercing the target.
   */
  async animateProxyTo(targetElement, alignSpec = { align: "c-c" }) {
    const { context, draggedElements } = this, { element } = context, targetRect = targetElement.isRectangle ? targetElement : Rectangle.from(targetElement);
    draggedElements.forEach((el) => {
      el.classList.add("b-drag-final-transition");
      DomHelper.alignTo(el, targetRect, alignSpec, void 0, void 0, this.positioning === "inset" ? "topInset" : "translate");
    });
    await EventHelper.waitForTransitionEnd({
      element,
      property: "all",
      thisObj: this,
      once: true
    });
    draggedElements.forEach((el) => el.classList.remove("b-drag-final-transition"));
  }
  /**
   * Returns true if a drag operation is active
   * @property {Boolean}
   * @readonly
   */
  get isDragging() {
    var _a5;
    return Boolean((_a5 = this.context) == null ? void 0 : _a5.started);
  }
  //#region Salesforce hooks
  getMouseMoveEventTarget(event) {
    return !event.isScroll ? event.target : DomHelper.elementFromPoint(event.clientX, event.clientY);
  }
  //#endregion
};
DragHelper._$name = "DragHelper";

// ../Core/lib/Core/helper/DomSync.js
var arraySlice2 = Array.prototype.slice;
var emptyArray5 = Object.freeze([]);
var emptyObject6 = Object.freeze({});
var htmlRe2 = /[&<]/;
var { getPrototypeOf: getPrototypeOf2 } = Object;
var { toString: toString4 } = Object.prototype;
var { isEqual, isObject: isObject3 } = ObjectHelper;
var checkEqualityIgnore = {
  parent: 1,
  elementData: 1,
  ns: 1,
  syncOptions: 1
};
var makeCheckEqualityOptions = () => ({
  ignore: checkEqualityIgnore,
  refsFound: /* @__PURE__ */ new Set()
});
var isClass = {
  class: 1,
  className: 1,
  classname: 1
};
var simpleTypes = {
  bigint: 1,
  boolean: 1,
  function: 1,
  number: 1,
  // object
  string: 1,
  symbol: 1
  // undefined
};
var syncIgnoreAttributes = {
  tag: 1,
  html: 1,
  text: 1,
  children: 1,
  tooltip: 1,
  parent: 1,
  nextSibling: 1,
  ns: 1,
  reference: 1,
  elementData: 1,
  retainElement: 1,
  compareHtml: 1,
  syncOptions: 1,
  listeners: 1,
  // eslint-disable-line bryntum/no-listeners-in-lib
  isReleased: 1,
  null: 1,
  "": 1,
  keepChildren: 1,
  retainChildren: 1
};
var addAndCacheCls = (cls, lastDomConfig) => {
  const propertyName = "className" in lastDomConfig ? "className" : "class", propertyValue = lastDomConfig[propertyName];
  if (propertyValue) {
    if (typeof propertyValue === "string") {
      const value2 = propertyValue.split(" ");
      if (!value2.includes(cls)) {
        value2.push(cls);
        lastDomConfig[propertyName] = value2.join(" ");
      }
    } else if (Array.isArray(propertyValue)) {
      if (!propertyValue.includes(cls)) {
        propertyValue.push(cls);
      }
    } else if (propertyValue.isDomClassList) {
      propertyValue.add(cls);
    } else if (ObjectHelper.isObject(propertyValue)) {
      propertyValue[cls] = 1;
    }
  }
};
var removeAndUncacheCls = (cls, lastDomConfig) => {
  const propertyName = "className" in lastDomConfig ? "className" : "class", propertyValue = lastDomConfig[propertyName];
  if (propertyValue) {
    if (typeof propertyValue === "string") {
      const value2 = propertyValue.split(" ");
      if (value2.includes(cls)) {
        value2.splice(value2.indexOf(cls), 1);
        lastDomConfig[propertyName] = value2.join(" ");
      }
    } else if (Array.isArray(propertyValue)) {
      if (propertyValue.includes(cls)) {
        propertyValue.splice(propertyValue.indexOf(cls), 1);
      }
    } else if (propertyValue.isDomClassList) {
      propertyValue.remove(cls);
    } else if (ObjectHelper.isObject(propertyValue)) {
      delete propertyValue[cls];
    }
  }
};
var DomSync = class _DomSync {
  /**
   * Compares two DOM configs or properties of such objects for equality.
   * @param {Object} is The new value.
   * @param {Object} was The old value.
   * @param {Object} options An object with various options to control the comparison.
   * @param {Object} options.ignore An object containing names of attributes to ignore having `true` value.
   * @param {Map} options.equalityCache A map that can be used to record equality results for objects to avoid
   * recomputing the result for the same objects.
   * @param {Set} options.refsFound A Set that must be populated with the values of any `reference` properties found.
   * @param {Boolean|String} [ignoreRefs] Pass `true` to ignore `reference` properties on domConfigs. Pass `'children'`
   * to ignore `reference` properties only on child element configs.
   * @returns {Boolean}
   * @private
   */
  static checkEquality(is, was, options, ignoreRefs) {
    var _a5;
    if (is === was) {
      return true;
    }
    if (is == null) {
      return was == null;
    }
    if (!is || !was) {
      return false;
    }
    const typeA = typeof is, typeB = typeof was;
    if (typeA !== typeB || simpleTypes[typeA]) {
      return false;
    }
    let cache = options.equalityCache || (options.equalityCache = /* @__PURE__ */ new Map()), ignoreChildRefs = Boolean(ignoreRefs), equal, i, ignore, ignoreRefOpt, key, syncOptions, val;
    cache = cache.get(is) || cache.set(is, /* @__PURE__ */ new Map()).get(is);
    equal = cache.get(was);
    if (equal === void 0) {
      equal = true;
      if (getPrototypeOf2(is) !== getPrototypeOf2(was) || is instanceof Node) {
        equal = false;
      } else if (Array.isArray(is)) {
        i = is.length;
        if (i !== was.length) {
          equal = false;
        } else {
          while (i-- > 0) {
            if (!_DomSync.checkEquality(is[i], was[i], options, ignoreChildRefs)) {
              equal = false;
              break;
            }
          }
        }
      } else {
        syncOptions = is.syncOptions;
        ignoreRefOpt = syncOptions == null ? void 0 : syncOptions.ignoreRefs;
        if (ignoreRefOpt) {
          ignoreChildRefs = true;
          ignoreRefs = ignoreRefOpt !== "children";
        }
        ignore = options.ignore || emptyObject6;
        for (key in was) {
          if (!ignore[key] && !(key in is) && !(ignoreRefs && key === "reference")) {
            equal = false;
            break;
          }
        }
        if (equal) {
          if (toString4.call(was) === "[object Date]") {
            equal = is.getTime() === was.getTime();
          } else if (DomHelper.isReactElement(is) || DomHelper.isReactElement(was) || DomHelper.isVueConfig(is) || DomHelper.isVueConfig(was)) {
            equal = false;
          } else {
            for (key in is) {
              if (!ignore[key] && !(ignoreRefs && key === "reference")) {
                if (!(key in was)) {
                  equal = false;
                  break;
                }
                val = is[key];
                if (!_DomSync.checkEquality(val, was[key], options, ignoreChildRefs)) {
                  equal = false;
                  break;
                }
              }
            }
          }
        }
      }
      if (!ignoreRefs && isObject3(is) && is.reference) {
        (_a5 = options.refsFound) == null ? void 0 : _a5.add(is.reference);
      }
      cache.set(was, equal);
    }
    return equal;
  }
  /**
   * Sync a DOM config to a target element
   * @param {Object} options Options object
   * @param {DomConfig} options.domConfig A DOM config object
   * @param {HTMLElement} options.targetElement Target element to apply to
   * @param {Boolean} [options.strict=false] Specify `true` to limit synchronization to only the values set by
   * previous calls. Styles and classes placed directly on the DOM elements by other means will not be affected.
   * @param {String} [options.syncIdField] Field in dataset to use to match elements for re-usage
   * @param {String|String[]} [options.affected] The references affected by a partial sync.
   * @param {Function} [options.callback] A function that will be called on element re-usage, creation and similar
   * @param {Boolean} [options.configEquality] A function that will be called to compare an incoming config to
   * the last config applied to the `targetElement`. This function returns `true` if the passed values are equal and
   * `false` otherwise.
   * @returns {HTMLElement} Returns the updated target element (which is also updated in place)
   */
  static sync(options) {
    var _a5;
    const optionsIn = options, { refOwner } = options, refsWas = refOwner == null ? void 0 : refOwner.byRef, checkEqualityOptions = makeCheckEqualityOptions();
    let affected = options.affected, i, ref, targetNode, lastDomConfig;
    if (typeof affected === "string") {
      affected = [affected];
    }
    options = {
      ...options,
      checkEqualityOptions
    };
    if (refOwner) {
      refOwner.byRef = {};
      if (affected) {
        for (ref in refsWas) {
          if (!affected.includes(ref)) {
            refOwner.byRef[ref] = refsWas[ref];
          }
        }
      }
      options.refsWas = refsWas;
    }
    optionsIn.changed = _DomSync.performSync(options, options.targetElement);
    if (refOwner) {
      if (!affected) {
        affected = Object.keys(refsWas);
      }
      for (i = 0; i < affected.length; ++i) {
        ref = affected[i];
        targetNode = refsWas[ref];
        if (checkEqualityOptions.refsFound.has(ref) || targetNode.retainElement) {
          refOwner.byRef[ref] = targetNode;
        } else {
          lastDomConfig = targetNode.lastDomConfig;
          (_a5 = targetNode.blur) == null ? void 0 : _a5.call(targetNode);
          targetNode.remove();
          refOwner.detachRef(ref, targetNode, lastDomConfig);
        }
      }
    }
    return options.targetElement;
  }
  static performSync(options, targetElement) {
    const { domConfig, callback } = options, { lastDomConfig } = targetElement, configIsEqual = options.configEquality || _DomSync.checkEquality;
    if (!configIsEqual(domConfig, lastDomConfig, options.checkEqualityOptions, options.ignoreRefs)) {
      if (domConfig) {
        if (!domConfig.onlyChildren) {
          _DomSync.syncAttributes(domConfig, targetElement, options);
          _DomSync.syncContent(domConfig, targetElement);
        }
        if (!domConfig.keepChildren) {
          _DomSync.syncChildren(options, targetElement);
        }
      } else {
        targetElement.innerHTML = null;
        targetElement.syncIdMap = null;
      }
      targetElement.lastDomConfig = !((domConfig == null ? void 0 : domConfig.onlyChildren) && lastDomConfig) ? domConfig : {
        ...lastDomConfig,
        children: domConfig.children
      };
      return true;
    } else {
      callback == null ? void 0 : callback({
        action: "none",
        domConfig,
        targetElement
      });
    }
    return false;
  }
  //region Attributes
  static syncDataset(domConfig, targetElement) {
    const { lastDomConfig } = targetElement, sameConfig = domConfig === lastDomConfig, source = Object.keys(domConfig.dataset), target = lastDomConfig && lastDomConfig.dataset && Object.keys(lastDomConfig.dataset), delta = ArrayHelper.delta(source, target);
    let attr, i, name, value2;
    for (i = 0; i < delta.onlyInA.length; i++) {
      attr = delta.onlyInA[i];
      value2 = domConfig.dataset[attr];
      if (value2 != null) {
        targetElement.setAttribute(`data-${StringHelper.hyphenate(attr)}`, value2);
      }
    }
    for (i = 0; i < delta.inBoth.length; i++) {
      attr = delta.inBoth[i];
      value2 = domConfig.dataset[attr];
      if (sameConfig || value2 != lastDomConfig.dataset[attr]) {
        name = `data-${StringHelper.hyphenate(attr)}`;
        if (value2 == null) {
          targetElement.removeAttribute(name);
        } else {
          targetElement.setAttribute(name, value2);
        }
      }
    }
    for (i = 0; i < delta.onlyInB.length; i++) {
      targetElement.removeAttribute(`data-${StringHelper.hyphenate(delta.onlyInB[i])}`);
    }
  }
  /**
   * Adds CSS classes to the element and to the cache.
   * @param {Core.helper.util.DomClassList|String|String[]|Object} cls
   * @param {HTMLElement} targetElement A previously DomSynced element
   * @internal
   */
  static addCls(cls, targetElement) {
    const { lastDomConfig } = targetElement;
    cls = DomClassList.normalize(cls, "array");
    cls.forEach((cls2) => {
      targetElement.classList.add(cls2);
      addAndCacheCls(cls2, lastDomConfig);
    });
  }
  /**
   * Adds CSS classes from the element and from the cache.
   * @param {Core.helper.util.DomClassList|String|String[]|Object} cls
   * @param {HTMLElement} targetElement A previously DomSynced element
   * @internal
   */
  static removeCls(cls, targetElement) {
    const { lastDomConfig } = targetElement;
    cls = DomClassList.normalize(cls, "array");
    cls.forEach((cls2) => {
      targetElement.classList.remove(cls2);
      removeAndUncacheCls(cls2, lastDomConfig);
    });
  }
  static syncClassList(domConfig, targetElement, lastDomConfig) {
    let cls = domConfig.className || domConfig.class, changed = false, c, currentClasses, i, k, keep, last;
    if (lastDomConfig) {
      currentClasses = DomClassList.normalize(targetElement, "array");
      cls = DomClassList.normalize(cls, "object");
      last = DomClassList.normalize(lastDomConfig.className || lastDomConfig.class, "object");
      keep = [];
      for (i = 0, k = currentClasses.length; i < k; ++i) {
        c = currentClasses[i];
        if (cls[c] || !(c in last)) {
          last[c] = 1;
          keep.push(c);
        } else {
          changed = true;
        }
      }
      for (c in cls) {
        if (!last[c]) {
          keep.push(c);
          changed = true;
        }
      }
      if (!changed) {
        return;
      }
      cls = keep.join(" ");
    } else {
      cls = DomClassList.normalize(cls);
    }
    targetElement.setAttribute("class", cls);
  }
  // Attributes as map { attr : value, ... }
  static getSyncAttributes(domConfig) {
    const attributes = {}, names2 = [];
    if (domConfig) {
      Object.keys(domConfig).forEach((attr) => {
        if (!syncIgnoreAttributes[attr]) {
          const name = attr.toLowerCase();
          attributes[name] = domConfig[attr];
          names2.push(name);
        }
      });
    }
    return { attributes, names: names2 };
  }
  static syncAttributes(domConfig, targetElement, options) {
    const { lastDomConfig } = targetElement, sameConfig = domConfig === lastDomConfig, sourceSyncAttrs = _DomSync.getSyncAttributes(domConfig), {
      attributes: sourceAttributes,
      names: sourceNames
    } = sourceSyncAttrs, {
      attributes: targetAttributes,
      names: targetNames
    } = sameConfig ? sourceSyncAttrs : _DomSync.getSyncAttributes(lastDomConfig), {
      onlyInA: toAdd,
      onlyInB: toRemove,
      inBoth: toSync
    } = sameConfig ? {
      onlyInA: emptyArray5,
      onlyInB: emptyArray5,
      inBoth: sourceNames
    } : ArrayHelper.delta(sourceNames, targetNames);
    let attr, i, handledClass = false;
    for (i = 0; i < toAdd.length; i++) {
      attr = toAdd[i];
      const sourceAttr = sourceAttributes[attr];
      if (attr === "style" && sourceAttr != null) {
        DomHelper.applyStyle(targetElement, sourceAttr, true);
      } else if (attr === "dataset") {
        _DomSync.syncDataset(domConfig, targetElement);
      } else if (isClass[attr]) {
        _DomSync.syncClassList(domConfig, targetElement);
        handledClass = true;
      } else if (sourceAttr != null) {
        targetElement.setAttribute(attr, sourceAttr);
      }
    }
    for (i = 0; i < toSync.length; i++) {
      attr = toSync[i];
      const sourceAttr = sourceAttributes[attr], targetAttr = targetAttributes[attr];
      if (sourceAttr == null) {
        targetElement.removeAttribute(attr);
      } else if (attr === "style") {
        if (options.strict) {
          if (sameConfig) {
            _DomSync.syncStyles(targetElement, sourceAttr);
          } else if (!isEqual(sourceAttr, targetAttr, true)) {
            _DomSync.syncStyles(targetElement, sourceAttr, targetAttr);
          }
        } else if (sameConfig || !isEqual(sourceAttr, targetAttr, true)) {
          DomHelper.applyStyle(targetElement, sourceAttr, true);
        }
      } else if (attr === "dataset") {
        _DomSync.syncDataset(domConfig, targetElement);
      } else if (isClass[attr]) {
        _DomSync.syncClassList(domConfig, targetElement, options.strict && targetElement.lastDomConfig);
        handledClass = true;
      } else if (sameConfig || sourceAttr !== targetAttr) {
        targetElement.setAttribute(attr, sourceAttr);
      }
    }
    for (i = 0; i < toRemove.length; i++) {
      attr = toRemove[i];
      if (!isClass[attr] || !handledClass) {
        targetElement.removeAttribute(attr);
      }
    }
  }
  static syncStyles(targetElement, sourceAttr, targetAttr) {
    let styles2, key, value2;
    if (!targetAttr) {
      styles2 = sourceAttr;
    } else {
      styles2 = {};
      sourceAttr = DomHelper.parseStyle(sourceAttr);
      targetAttr = DomHelper.parseStyle(targetAttr);
      if (sourceAttr) {
        for (key in sourceAttr) {
          value2 = sourceAttr[key];
          if (targetAttr[key] !== value2) {
            styles2[key] = value2;
          }
        }
      }
      for (key in targetAttr) {
        if (!(key in sourceAttr)) {
          styles2[key] = "";
        }
      }
    }
    DomHelper.applyStyle(targetElement, styles2);
  }
  //endregion
  //region Content
  static syncContent(domConfig, targetElement) {
    var _a5;
    const { html, text } = domConfig, content = text != null ? text : html;
    targetElement.didSetTextContent = false;
    if (domConfig.elementData) {
      targetElement.elementData = domConfig.elementData;
    }
    if (content instanceof DocumentFragment) {
      if (targetElement.childNodes.length === 1 && DomHelper.getChildElementCount(targetElement) === 0 && content.childNodes.length === 1 && DomHelper.getChildElementCount(content) === 0) {
        DomHelper.setInnerText(targetElement, content.firstChild.data);
      } else {
        targetElement.innerHTML = "";
        targetElement.appendChild(content);
      }
    } else if (html != null && htmlRe2.test(html)) {
      targetElement.innerHTML = String(html);
    } else if (content != null) {
      const { lastDomConfig } = targetElement;
      if (((_a5 = lastDomConfig == null ? void 0 : lastDomConfig.children) == null ? void 0 : _a5.length) || (lastDomConfig == null ? void 0 : lastDomConfig.html) && htmlRe2.test(targetElement.lastDomConfig.html)) {
        targetElement.innerHTML = "";
      }
      DomHelper.setInnerText(targetElement, String(content));
      targetElement.didSetTextContent = true;
    }
  }
  static insertTextNode(text, targetElement, callback, refOwner, beforeElement = null) {
    const newNode = document.createTextNode(text);
    targetElement.insertBefore(newNode, beforeElement);
    if (refOwner) {
      newNode.$refOwnerId = refOwner.id;
    }
    callback == null ? void 0 : callback({
      action: "newNode",
      domConfig: text,
      targetElement: newNode
    });
  }
  static insertElement(domConfig, targetElement, targetNode, refOwner, syncIdMap, syncId, options) {
    var _a5;
    const newElement = options.ns ? document.createElementNS(options.ns, domConfig.tag || "svg") : document.createElement(domConfig.tag || "div");
    if (domConfig.elementData) {
      newElement.elementData = domConfig.elementData;
    }
    targetElement.insertBefore(newElement, targetNode);
    _DomSync.performSync(options, newElement);
    if (syncId != null) {
      syncIdMap[syncId] = newElement;
    }
    if (!("role" in domConfig) && !domConfig.tabIndex && !domConfig["aria-hidden"] && !domConfig.alt && !DomHelper.isFocusable(newElement, true) && !newElement.htmlFor) {
      newElement.setAttribute("role", "presentation");
    }
    if (refOwner) {
      newElement.$refOwnerId = refOwner.id;
      if (syncId) {
        newElement.$reference = syncId;
        refOwner.attachRef(syncId, newElement, domConfig);
      }
    }
    (_a5 = options.callback) == null ? void 0 : _a5.call(options, {
      action: "newElement",
      domConfig,
      targetElement: newElement,
      syncId
    });
  }
  //endregion
  //region Children
  static syncChildren(options, targetElement) {
    var _a5, _b2, _c2, _d;
    let {
      domConfig,
      syncIdField,
      callback,
      releaseThreshold,
      configEquality,
      ns: ns3,
      refOwner,
      refsWas,
      strict,
      checkEqualityOptions,
      ignoreRefs
    } = options, cleanupNodes = null, index, nextNode, syncId;
    const syncOptions = domConfig.syncOptions || {};
    if ((_a5 = domConfig.html) != null ? _a5 : domConfig.text) {
      return;
    }
    ignoreRefs = Boolean((_b2 = syncOptions.ignoreRefs) != null ? _b2 : ignoreRefs);
    if (ignoreRefs) {
      refOwner = refsWas = null;
    }
    if ("strict" in syncOptions) {
      strict = syncOptions.strict;
    }
    const newSyncIdMap = refOwner ? refOwner.byRef : {}, sourceConfigs = arraySlice2.call(domConfig.children || []), targetNodes = arraySlice2.call(targetElement.childNodes), syncIdMap = refsWas || targetElement.syncIdMap || {}, releasedIdMap = targetElement.releasedIdMap || {}, nextTarget = (remove) => {
      while (targetNodes.length && targetNodes[0].parentNode !== targetElement) {
        targetNodes.shift();
      }
      return (remove ? targetNodes.shift() : targetNodes[0]) || null;
    };
    syncIdField = syncOptions.syncIdField || syncIdField;
    strict = syncOptions.strict || strict;
    callback = syncOptions.callback || callback;
    configEquality = syncOptions.configEquality || configEquality;
    releaseThreshold = "releaseThreshold" in syncOptions ? syncOptions.releaseThreshold : releaseThreshold;
    if (syncIdField) {
      targetElement.syncIdMap = newSyncIdMap;
    }
    const syncChildOptions = {
      checkEqualityOptions: checkEqualityOptions || makeCheckEqualityOptions(),
      ignoreRefs,
      refOwner,
      refsWas,
      strict,
      syncIdField,
      releaseThreshold,
      callback,
      configEquality
    };
    while (sourceConfigs.length) {
      const sourceConfig = sourceConfigs.shift();
      syncId = null;
      if (!sourceConfig) {
        continue;
      }
      if (DomHelper.isReactElement(sourceConfig) || DomHelper.isVueConfig(sourceConfig)) {
        callback({ jsx: sourceConfig, targetElement, domConfig });
        continue;
      }
      if (Array.isArray(sourceConfig)) {
        this.syncChildren({
          ...options,
          domConfig: {
            ...options.domConfig,
            children: sourceConfig
          }
        }, targetElement);
        continue;
      }
      if (sourceConfig instanceof Node) {
        nextNode = nextTarget();
        if (sourceConfig !== nextNode) {
          targetElement.insertBefore(sourceConfig, nextNode);
        }
        index = targetNodes.indexOf(sourceConfig);
        if (index > -1) {
          targetNodes.splice(index, 1);
        }
        continue;
      }
      const isTextNode = typeof sourceConfig === "string";
      syncChildOptions.domConfig = sourceConfig;
      syncChildOptions.ns = sourceConfig.ns || ns3;
      if (!isTextNode) {
        if (refOwner) {
          syncId = sourceConfig.reference;
        } else if (syncIdField && sourceConfig.dataset) {
          syncId = sourceConfig.dataset[syncIdField];
        }
        if (syncId != null && !sourceConfig.unmatched) {
          const syncTargetElement = syncIdMap[syncId] || releasedIdMap[syncId];
          if (syncTargetElement) {
            const { lastDomConfig } = syncTargetElement;
            if (syncTargetElement.retainElement && syncTargetElement.retainElement !== "prevent-release") {
            } else if (_DomSync.performSync(syncChildOptions, syncTargetElement)) {
              callback == null ? void 0 : callback({
                action: "reuseOwnElement",
                domConfig: sourceConfig,
                targetElement: syncTargetElement,
                lastDomConfig,
                syncId
              });
            }
            if (sourceConfig.retainElement) {
              syncTargetElement.retainElement = true;
            }
            newSyncIdMap[syncId] = syncTargetElement;
            ArrayHelper.remove(targetNodes, syncTargetElement);
            delete releasedIdMap[syncId];
            syncTargetElement.isReleased = false;
            nextNode = nextTarget();
            if (syncTargetElement.parentNode !== targetElement || strict && syncTargetElement.nextSibling !== nextNode) {
              targetElement.insertBefore(syncTargetElement, nextNode);
            }
          } else if (strict) {
            _DomSync.insertElement(
              sourceConfig,
              targetElement,
              nextTarget(),
              refOwner,
              newSyncIdMap,
              syncId,
              syncChildOptions
            );
          } else {
            sourceConfigs.push(sourceConfig);
            sourceConfig.unmatched = true;
          }
          continue;
        }
        if (sourceConfig.unmatched) {
          delete sourceConfig.unmatched;
        }
      }
      let beforeNode = null, targetNode = null, cleanupNode;
      while (!targetNode && (cleanupNode = nextTarget(true))) {
        if (refOwner) {
          if (cleanupNode.$refOwnerId !== refOwner.id) {
            continue;
          }
          if (cleanupNode.$reference) {
            if (!strict) {
              continue;
            }
            beforeNode = cleanupNode;
            break;
          }
          targetNode = cleanupNode;
        } else if (!cleanupNode.retainElement) {
          targetNode = cleanupNode;
        }
        if (!targetNode) {
          (cleanupNodes || (cleanupNodes = [])).push(cleanupNode);
        }
      }
      if (beforeNode || !targetNode) {
        if (isTextNode) {
          _DomSync.insertTextNode(sourceConfig, targetElement, callback, refOwner, beforeNode);
        } else {
          _DomSync.insertElement(
            sourceConfig,
            targetElement,
            beforeNode,
            refOwner,
            newSyncIdMap,
            syncId,
            syncChildOptions
          );
        }
      } else {
        if (!isTextNode && targetNode.nodeType === Node.ELEMENT_NODE && (sourceConfig.tag || "div").toLowerCase() === targetNode.tagName.toLowerCase()) {
          const { lastDomConfig } = targetNode, result = _DomSync.performSync(syncChildOptions, targetNode);
          if (syncIdField && ((_c2 = lastDomConfig == null ? void 0 : lastDomConfig.dataset) == null ? void 0 : _c2[syncIdField]) != null) {
            const lastSyncId = lastDomConfig.dataset[syncIdField];
            delete releasedIdMap[lastSyncId];
            delete syncIdMap[lastSyncId];
          }
          if (syncId != null) {
            newSyncIdMap[syncId] = targetNode;
          }
          targetNode.isReleased = false;
          result && (callback == null ? void 0 : callback({
            action: "reuseElement",
            domConfig: sourceConfig,
            targetElement: targetNode,
            lastDomConfig,
            syncId
          }));
        } else if (isTextNode && targetNode.nodeType === Node.TEXT_NODE) {
          targetNode.data = sourceConfig;
        } else {
          if (isTextNode) {
            _DomSync.insertTextNode(sourceConfig, targetElement, callback, refOwner, targetNode);
          } else {
            _DomSync.insertElement(
              sourceConfig,
              targetElement,
              targetNode,
              refOwner,
              newSyncIdMap,
              syncId,
              syncChildOptions
            );
          }
          (_d = targetNode.blur) == null ? void 0 : _d.call(targetNode);
          targetNode.remove();
        }
      }
    }
    if (targetElement.didSetTextContent && targetNodes.length === 1 && targetNodes[0].nodeType === Node.TEXT_NODE) {
      return;
    }
    if (domConfig.retainChildren) {
      return;
    }
    while (nextNode = nextTarget(true)) {
      if (!refOwner || nextNode.$refOwnerId === refOwner.id && !nextNode.$reference) {
        (cleanupNodes || (cleanupNodes = [])).push(nextNode);
      }
    }
    if (cleanupNodes) {
      _DomSync.syncChildrenCleanup(
        targetElement,
        cleanupNodes,
        newSyncIdMap,
        callback,
        refOwner,
        releaseThreshold,
        syncIdField
      );
    }
  }
  static syncChildrenCleanup(targetElement, cleanupNodes, newSyncIdMap, callback, refOwner, releaseThreshold, syncIdField) {
    var _a5;
    let releaseCount = 0, ref;
    for (const targetNode of cleanupNodes) {
      const { lastDomConfig } = targetNode;
      if (targetNode.nodeType === Element.TEXT_NODE) {
        targetNode.remove();
        continue;
      }
      if (!targetNode.retainElement) {
        if (!refOwner && syncIdField && (releaseThreshold == null || releaseCount < releaseThreshold)) {
          if (!targetNode.isReleased) {
            targetNode.className = "b-released";
            targetNode.isReleased = true;
            if (lastDomConfig == null ? void 0 : lastDomConfig.dataset) {
              if (!targetElement.releasedIdMap) {
                targetElement.releasedIdMap = {};
              }
              targetElement.releasedIdMap[lastDomConfig.dataset[syncIdField]] = targetNode;
            }
            callback == null ? void 0 : callback({
              action: "releaseElement",
              domConfig: lastDomConfig,
              lastDomConfig,
              targetElement: targetNode
            });
            if (lastDomConfig) {
              lastDomConfig.isReleased = true;
              if (lastDomConfig.className) {
                lastDomConfig.className = "b-released";
              }
              if (lastDomConfig.class) {
                lastDomConfig.class = "b-released";
              }
              if (lastDomConfig.style) {
                lastDomConfig.style = null;
              }
            }
          }
          releaseCount++;
        } else {
          (_a5 = targetNode.blur) == null ? void 0 : _a5.call(targetNode);
          targetNode.remove();
          if (refOwner) {
            ref = targetNode.$reference;
            if (ref) {
              refOwner.detachRef(ref, targetNode, lastDomConfig);
            }
          }
          if (targetElement.releasedIdMap && syncIdField && (lastDomConfig == null ? void 0 : lastDomConfig.dataset)) {
            delete targetElement.releasedIdMap[lastDomConfig.dataset[syncIdField]];
          }
          callback == null ? void 0 : callback({
            action: "removeElement",
            domConfig: targetNode.lastDomConfig,
            lastDomConfig: targetNode.lastDomConfig,
            targetElement: targetNode
          });
        }
      } else if (syncIdField) {
        if (lastDomConfig) {
          newSyncIdMap[targetNode.dataset[syncIdField]] = targetNode;
        }
      }
    }
  }
  /**
   * Remove a child element without syncing, for example when dragging an element to some other parent.
   * Removes it both from DOM and the parent elements syncMap
   * @param {HTMLElement} parentElement
   * @param {HTMLElement} childElement
   */
  static removeChild(parentElement, childElement) {
    if (parentElement.contains(childElement)) {
      const syncIdMap = parentElement.syncIdMap;
      if (syncIdMap) {
        const index = Object.values(syncIdMap).indexOf(childElement);
        if (index > -1) {
          delete syncIdMap[Object.keys(syncIdMap)[index]];
        }
      }
      parentElement.removeChild(childElement);
    }
  }
  /**
   * Adds a child element without syncing, making it properly available for later syncs. Useful for example
   * when dragging and dropping an element from some other parent.
   * @param {HTMLElement} parentElement
   * @param {HTMLElement} childElement
   * @param {String|Number} syncId
   */
  static addChild(parentElement, childElement, syncId) {
    parentElement.appendChild(childElement);
    if (!parentElement.syncIdMap) {
      parentElement.syncIdMap = {};
    }
    parentElement.syncIdMap[syncId] = childElement;
  }
  /**
   * Get a child element using a dot separated syncIdMap path.
   *
   * ```javascript
   * DomSync.getChild(eventWrap, 'event.percentBar');
   * ```
   *
   * @param {HTMLElement} element "root" element, under which the path starts
   * @param {String} path Dot '.' separated path of syncIdMap entries
   * @returns {HTMLElement} Child element or `null` if path did not match any element
   */
  static getChild(element, path) {
    var _a5;
    const syncIds = String(path).split(".");
    for (const id2 of syncIds) {
      element = (_a5 = element == null ? void 0 : element.syncIdMap) == null ? void 0 : _a5[id2];
      if (!element) {
        return null;
      }
    }
    return element;
  }
  //endregion
};
DomSync._$name = "DomSync";

// ../Core/lib/Core/helper/util/Fullscreen.js
var Fullscreen = class _Fullscreen {
  static init() {
    const fnNames = ["fullscreenEnabled", "requestFullscreen", "exitFullscreen", "fullscreenElement"], prefixFn = (prefix) => fnNames.map((fn2) => {
      let result = prefix + StringHelper.capitalize(fn2);
      if (prefix === "moz") {
        result = result.replace("screen", "Screen");
        if ("mozCancelFullScreen" in document && fn2 === "exitFullscreen") {
          result = "mozCancelFullScreen";
        }
      }
      return result;
    });
    this.functions = "fullscreenEnabled" in document && fnNames || "webkitFullscreenEnabled" in document && prefixFn("webkit") || "mozFullScreenEnabled" in document && prefixFn("moz") || "msFullscreenEnabled" in document && prefixFn("ms") || [];
    const eventNames = [
      "fullscreenchange",
      "fullscreenerror"
    ], msEventNames = [
      "MSFullscreenChange",
      "MSFullscreenError"
    ], prefixEvt = (prefix) => eventNames.map((eventName) => prefix + StringHelper.capitalize(eventName));
    this.events = "fullscreenEnabled" in document && eventNames || "webkitFullscreenEnabled" in document && prefixEvt("webkit") || "mozFullscreenEnabled" in document && prefixEvt("moz") || "msFullscreenEnabled" in document && msEventNames || [];
    this.onFullscreenChange(this.onInternalFullscreenChange.bind(this));
  }
  /**
   * True if the fullscreen mode is supported and enabled, false otherwise
   * @property {Boolean}
   */
  static get enabled() {
    return Boolean(this.functions[0] && document[this.functions[0]]);
  }
  /**
   * Request entering the fullscreen mode.
   * @param {HTMLElement} element Element to be displayed fullscreen
   * @returns {Promise} A promise which is resolved with a value of undefined when the transition to full screen is complete.
   */
  static async request(element) {
    return this.functions[1] && (element == null ? void 0 : element[this.functions[1]]());
  }
  /**
   * Exit the previously entered fullscreen mode.
   * @returns {Promise} A promise which is resolved once the user agent has finished exiting full-screen mode
   */
  static async exit() {
    return this.functions[2] && document[this.functions[2]]();
  }
  /**
   * True if fullscreen mode is currently active, false otherwise
   * @property {Boolean}
   */
  static get isFullscreen() {
    return Boolean(this.element);
  }
  static get element() {
    return this.functions[3] && document[this.functions[3]];
  }
  /**
   * Installs the passed listener to fullscreenchange event
   * @param {Function} fn The listener to install
   */
  static onFullscreenChange(fn2) {
    if (this.events[0]) {
      document.addEventListener(this.events[0], fn2);
    }
  }
  /**
   * Uninstalls the passed listener from fullscreenchange event
   * @param {Function} fn
   */
  static unFullscreenChange(fn2) {
    if (this.events[0]) {
      document.removeEventListener(this.events[0], fn2);
    }
  }
  // Make sure the floatRoot is added to any element going fullscreen
  static onInternalFullscreenChange(event) {
    const me = globalThis.bryntum, { element: fullscreenElement } = _Fullscreen;
    if (_Fullscreen.isFullscreen && me._floatRoot || !_Fullscreen.isFullscreen && !me._floatRoot) {
      return;
    }
    let floatRoot, floatParent;
    if (fullscreenElement) {
      if (!fullscreenElement.closest(".b-floatroot")) {
        const rootElement = DomHelper.getRootElement(fullscreenElement);
        floatRoot = rootElement.floatRoot;
        if (floatRoot) {
          me._floatRoot = floatRoot;
          me._oldParent = floatRoot.parentElement;
          floatParent = fullscreenElement;
        }
      }
    } else {
      floatRoot = me._floatRoot;
      floatParent = floatRoot && me._oldParent;
      me._oldParent = me._floatRoot = null;
    }
    if (floatParent) {
      const floatingWidgets = Array.from(floatRoot.querySelectorAll(".b-widget")).map((e) => Widget.fromElement(e));
      floatingWidgets.forEach((fw) => {
        var _a5;
        return (_a5 = fw.beforeFloatReparent) == null ? void 0 : _a5.call(fw);
      });
      floatParent.appendChild(floatRoot);
      floatingWidgets.forEach((fw) => {
        var _a5;
        return (_a5 = fw.afterFloatReparent) == null ? void 0 : _a5.call(fw);
      });
    }
    GlobalEvents_default.trigger("fullScreenChange", { event });
  }
};
Fullscreen.init();
Fullscreen._$name = "Fullscreen";

// ../Core/lib/Core/helper/ResizeMonitor.js
var resizeFireTimer = null;
var resizedQueue = [];
var isAbsolutelyPositioned = (n) => {
  var _a5;
  return n.nodeType === n.ELEMENT_NODE && ((_a5 = n.ownerDocument.defaultView) == null ? void 0 : _a5.getComputedStyle(n).getPropertyValue("position")) === "absolute";
};
var ResizeMonitor = class _ResizeMonitor {
  /**
   * Resize event handing callback
   * @callback ResizeMonitorEventCallback
   * @param {HTMLElement} element Resized element
   * @param {DOMRectReadOnly} oldRect Old size rectangle
   * @param {DOMRectReadOnly} newRect New size rectangle
   */
  /**
   * Adds a resize listener to the passed element which is called when the element
   * is resized by layout.
   * @param {HTMLElement} element The element to listen for resizing.
   * @param {ResizeMonitorEventCallback} handler The handling function. Will be passed the element.
   */
  static addResizeListener(element, handler) {
    const me = this;
    if (element === document || element === globalThis) {
      element = document.body;
    }
    if (element.nodeType === element.DOCUMENT_FRAGMENT_NODE) {
      element = element.host;
    }
    if (!element.classList.contains("b-resize-monitored")) {
      element.classList.add("b-resize-monitored");
      element._bResizemonitor = {
        handlers: []
      };
    }
    if (element === document.body) {
      if (!me.hasWindowResizeListener) {
        globalThis.addEventListener("resize", me.onWindowResize);
        me.hasWindowResizeListener = true;
      }
    } else if (globalThis.ResizeObserver) {
      if (!me.resizeObserver) {
        me.resizeObserver = new ResizeObserver(me.onElementResize);
      }
      me.resizeObserver.observe(element);
    } else {
      element.classList.add("b-no-resizeobserver");
      const [monitors, expand, shrink] = DomHelper.createElement({
        parent: element,
        className: "b-resize-monitors",
        children: [{
          className: "b-resize-monitor-expand"
        }, {
          className: "b-resize-monitor-shrink"
        }]
      }, { returnAll: true });
      expand.scrollLeft = expand.scrollTop = shrink.scrollLeft = shrink.scrollTop = 1e6;
      expand.addEventListener("scroll", me.onSizeMonitorScroll, true);
      shrink.addEventListener("scroll", me.onSizeMonitorScroll, true);
      (handler.targetMutationMonitor = new MutationObserver((m) => {
        const addedNodes = [], removedNodes = [];
        for (const mr of m) {
          if (mr.type === "childList") {
            addedNodes.push.apply(addedNodes, mr.addedNodes);
            removedNodes.push.apply(removedNodes, mr.removedNodes);
          }
        }
        const changedNodes = [
          ...addedNodes.filter((r) => !removedNodes.includes(r)),
          ...removedNodes.filter((r) => !addedNodes.includes(r))
        ];
        if (changedNodes.length === 0) {
          return;
        }
        if (changedNodes.length > 0 && changedNodes.every(isAbsolutelyPositioned)) {
          return;
        }
        expand.dispatchEvent(new CustomEvent("scroll", { bubbles: false }));
      })).observe(element, {
        childList: true,
        subtree: true
      });
      handler.monitorElement = monitors;
    }
    element._bResizemonitor.handlers.push(handler);
  }
  /**
   * Removes a resize listener from the passed element.
   * @param {HTMLElement} element The element to listen for resizing.
   * @param {Function} handler The handling function to remove.
   */
  static removeResizeListener(element, handler) {
    if (element) {
      if (element === document || element === globalThis) {
        element = document.body;
      }
      const resizeMonitor = element._bResizemonitor;
      let listenerCount = 0;
      if (resizeMonitor && resizeMonitor.handlers) {
        ArrayHelper.remove(resizeMonitor.handlers, handler);
        listenerCount = resizeMonitor.handlers.length;
      }
      if (!listenerCount) {
        element.classList.remove("b-resize-monitored");
        if (this.resizeObserver) {
          this.resizeObserver.unobserve(element);
        } else {
          if (handler.monitorElement) {
            handler.monitorElement.remove();
            handler.monitorElement = null;
          }
          if (handler.targetMutationMonitor) {
            handler.targetMutationMonitor.disconnect();
          }
        }
      }
    }
  }
  static onElementResize(entries) {
    for (const resizeObserverEntry of entries) {
      const resizedElement = resizeObserverEntry.target, resizeMonitor = resizedElement._bResizemonitor, newRect = resizeObserverEntry.contentRect || resizedElement.getBoundingClientRect();
      if (hasLayout(resizedElement)) {
        if (!resizeMonitor.rectangle || newRect.width !== resizeMonitor.rectangle.width || newRect.height !== resizeMonitor.rectangle.height) {
          const oldRect = resizeMonitor.rectangle;
          resizeMonitor.rectangle = newRect;
          for (const resizeHandler of resizeMonitor.handlers) {
            resizeHandler(resizedElement, oldRect, newRect);
          }
        }
      }
    }
  }
  static onSizeMonitorScroll(e) {
    var _a5;
    if (e.target.isConnected || ((_a5 = document.body) == null ? void 0 : _a5.contains(e.target))) {
      e.stopImmediatePropagation();
      const monitorNode = e.target.parentNode, resizedElement = monitorNode.parentNode, resizeMonitor = resizedElement._bResizemonitor, newRect = resizedElement.getBoundingClientRect();
      if (!resizeMonitor.rectangle || newRect.width !== resizeMonitor.rectangle.width || newRect.height !== resizeMonitor.rectangle.height) {
        resizedQueue.push([resizedElement, resizeMonitor.rectangle, newRect]);
        resizeMonitor.rectangle = newRect;
        if (!resizeFireTimer) {
          resizeFireTimer = requestAnimationFrame(_ResizeMonitor.fireResizeEvents);
        }
      }
      monitorNode.firstChild.scrollLeft = monitorNode.firstChild.scrollTop = monitorNode.childNodes[1].scrollTop = monitorNode.childNodes[1].scrollLeft = 1e6;
    }
  }
  static onWindowResize(e) {
    const resizedElement = document.body, resizeMonitor = resizedElement._bResizemonitor, oldRect = resizeMonitor.rectangle;
    resizeMonitor.rectangle = document.documentElement.getBoundingClientRect();
    for (const resizeHandler of resizeMonitor.handlers) {
      resizeHandler(resizedElement, oldRect, resizeMonitor.rectangle);
    }
  }
  static fireResizeEvents() {
    for (const resizedEntry of resizedQueue) {
      for (const resizeHandler of resizedEntry[0]._bResizemonitor.handlers) {
        if (resizedEntry[0].offsetParent) {
          resizeHandler.apply(this, resizedEntry);
        }
      }
    }
    resizeFireTimer = null;
    resizedQueue.length = 0;
  }
  static removeGlobalListeners() {
    globalThis.removeEventListener("resize", this.onWindowResize);
  }
};
ResizeMonitor._$name = "ResizeMonitor";

// ../Core/lib/Core/helper/util/Scroller.js
var scrollLiterals = {
  auto: "auto",
  true: "auto",
  false: "hidden",
  "hidden-scroll": "auto",
  clip: BrowserHelper.supportsOverflowClip ? "clip" : "hidden"
};
var scrollerCls = "b-widget-scroller";
var defaultScrollOptions = {
  block: "nearest"
};
var immediatePromise4 = Promise.resolve();
var scrollPromise = (element) => new Promise((resolve) => EventHelper.on({
  element: element === document.documentElement ? globalThis : element,
  scroll: resolve,
  once: true
}));
var xAxis = {
  x: 1
};
var isScrollable = {
  auto: 1,
  scroll: 1
};
var isScrollableConfig = {
  true: 1,
  auto: 1
};
var allScroll = {
  overflowX: "auto",
  overflowY: "auto"
};
var normalizeEdgeOffset = (edgeOffset) => {
  var _a5, _b2, _c2, _d;
  let top, bottom, start, end;
  if (!edgeOffset) {
    top = bottom = start = end = 0;
  } else if (typeof edgeOffset === "number") {
    top = bottom = start = end = edgeOffset;
  } else {
    top = (_a5 = edgeOffset.top) != null ? _a5 : 0;
    bottom = (_b2 = edgeOffset.bottom) != null ? _b2 : 0;
    start = (_c2 = edgeOffset.start) != null ? _c2 : 0;
    end = (_d = edgeOffset.end) != null ? _d : 0;
  }
  return {
    block: { start: top, end: bottom },
    inline: { start, end }
  };
};
var Scroller = class _Scroller extends Delayable_default(Events_default(Base)) {
  static get configurable() {
    return {
      /**
       * The widget which is to scroll.
       * @config {Core.widget.Widget}
       */
      widget: null,
      /**
       * The element which is to scroll. Defaults to the {@link Core.widget.Widget#property-overflowElement} of
       * the configured {@link #config-widget}
       * @config {HTMLElement}
       */
      element: {
        $config: {
          nullify: true
        },
        value: null
      },
      /**
       * The element, or a selector which identifies a descendant element whose size
       * will affect the scroll range.
       * @config {HTMLElement|String}
       */
      contentElement: {
        $config: {
          nullify: true
        },
        value: null
      },
      /**
       * How to handle overflowing in the `X` axis.
       * May be:
       * * `'auto'`
       * * `'visible'`
       * * `'hidden'`
       * * `'scroll'`
       * * `'hidden-scroll'` Meaning scrollable from the UI but with no scrollbar,
       * for example a grid header. Only on platforms which support this feature.
       * * `true` - meaning `'auto'`
       * * `false` - meaning `'hidden'`
       * * `clip` - Uses `clip` where supported. Where not supported it uses
       * `hidden` and rolls back any detected scrolls in this dimension.
       * @config {String|Boolean}
       */
      overflowX: null,
      /**
       * How to handle overflowing in the `Y` axis.
       * May be:
       * * `'auto'`
       * * `'visible'`
       * * `'hidden'`
       * * `'scroll'`
       * * `'hidden-scroll'` Meaning scrollable from the UI but with no scrollbar.
       * Only on platforms which support this feature.
       * * `true` - meaning `'auto'`
       * * `false` - meaning `'hidden'`
       * * `clip` - Uses `clip` where supported. Where not supported it uses
       * `hidden` and rolls back any detected scrolls in this dimension.
       * @config {String|Boolean}
       */
      overflowY: null,
      /**
       * If configured as `true`, the {@link #config-element} is not scrolled but is translated using CSS
       * transform when controlled by this class's API. Scroll events are fired when the element is translated.
       * @default
       * @config {Boolean}
       */
      translate: null,
      /**
       * Configures the [`scroll-behavior`](https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-behavior)
       * for the scrolling element. By default, this is controlled by CSS (i.e., `'auto'`).
       * @prp {'smooth'|'instant'|'auto'}
       * @default 'auto'
       * @internal
       */
      behavior: null,
      x: 0,
      y: 0,
      rtlSource: null,
      /**
       * Configure as `true` to immediately sync partner scrollers when being synced by a controlling partner
       * instead of waiting for our own `scroll` event to pass the scroll on to partners.
       * @prp {Boolean}
       * @default false
       */
      propagateSync: null,
      /**
       * CSS selector(s) of sticky elements, or the elements themselves which should be taken into account when scrolling.
       *
       * These are used to identify elements which are sticky and docked to occlude edges of
       * the scrolling element so that the viewport size can be calculated accurately when scrolling.
       * @config {String|String[]|HTMLElement|HTMLElement[]}
       * @internal
       */
      stickies: null
    };
  }
  static get delayable() {
    return {
      onScrollEnd: {
        type: "buffer",
        delay: 100
      }
    };
  }
  /**
   * Fired when scrolling happens on this Scroller's element. The event object is a native `scroll` event
   * with the described extra properties injected.
   * @event scroll
   * @param {Core.widget.Widget} widget The owning Widget which has been scrolled.
   * @param {Core.helper.util.Scroller} source This Scroller
   */
  /**
   * Fired when scrolling finished on this Scroller's element. The event object is the last native `scroll` event
   * fires by the element with the described extra properties injected.
   * @event scrollEnd
   * @param {Core.widget.Widget} widget The owning Widget which has been scrolled.
   * @param {Core.helper.util.Scroller} source This Scroller
   */
  /**
   * The `overflow-x` setting for the widget. `true` means `'auto'`.
   * @member {Boolean|String} overflowX
   */
  /**
   * The `overflow-y` setting for the widget. `true` means `'auto'`.
   * @member {Boolean|String} overflowY
   */
  get isRTL() {
    var _a5;
    return Boolean((_a5 = this.rtlSource) == null ? void 0 : _a5.rtl);
  }
  syncOverflowState() {
    const me = this, {
      hasOverflowX,
      hasOverflowY,
      element
    } = me, x = me.hasOverflowX = element.scrollWidth > element.clientWidth, y = me.hasOverflowY = element.scrollHeight > element.clientHeight;
    if (hasOverflowX !== x || hasOverflowY !== y) {
      const classList = new DomClassList(element.classList, {
        "b-horizontal-overflow": x,
        "b-vertical-overflow": y
      });
      DomHelper.syncClassList(element, classList);
      if (!me.isConfiguring) {
        me.trigger("overflowChange", { x, y });
      }
    }
  }
  /**
   * Returns `true` if there is overflow in the specified axis.
   * @param {'x'|'y'} [axis='y'] The axis to check scrollbar for. Note that this is subtly different to asking
   * whether an axis is showing a space-consuming scrollbar, see {@link #function-hasScrollbar}.
   * @internal
   */
  hasOverflow(axis = "y") {
    const me = this, overflowSetting = me[`overflow${axis.toUpperCase()}`], otherAxis = me[`overflow${axis === "y" ? "x" : "y"}`];
    if (!DomHelper.scrollBarWidth || !isScrollableConfig[overflowSetting] || otherAxis === "hidden-scroll") {
      const dimension = axis === "y" ? "Height" : "Width";
      return me[`scroll${dimension}`] > me[`client${dimension}`];
    }
    return me[`hasOverflow${axis.toUpperCase()}`];
  }
  /**
   * Returns `true` if there is a *space-consuming* scrollbar controlling scroll in the specified axis.
   * @param {'x'|'y'} [axis='y'] The axis to check scrollbar for. Note that this is subtly different to asking
   * whether an axis *has any* overflow, see {@link #function-hasOverflow}.
   * @internal
   */
  hasScrollbar(axis = "y") {
    const { element } = this;
    if (element && DomHelper.scrollBarWidth) {
      const vertical = axis === "y", dimension = vertical ? "Width" : "Height", clientSize = element[`client${dimension}`], borderSize = parseInt(DomHelper.getStyleValue(element, `border${vertical ? "Left" : "Top"}Width`)) + parseInt(DomHelper.getStyleValue(element, `border${vertical ? "Right" : "Bottom"}Width`)), difference = element[`offset${dimension}`] - borderSize - clientSize;
      return Math.abs(difference - DomHelper.scrollBarWidth) < 2;
    }
  }
  /**
   * Partners this Scroller with the passed scroller in order to sync the scrolling position in the passed axes
   * @param {Core.helper.util.Scroller} otherScroller
   * @param {String|Object} [axes='x'] `'x'` or `'y'` or `{x: true/false, y: true/false}` axes to sync
   * @param {Boolean} [axes.x] Sync horizontal scroll
   * @param {Boolean} [axes.y] Sync vertical scroll
   */
  addPartner(otherScroller, axes = xAxis) {
    const me = this;
    if (typeof axes === "string") {
      axes = {
        [axes]: 1
      };
    }
    if (!me.partners) {
      me.partners = {};
    }
    me.partners[otherScroller.id] = {
      scroller: otherScroller,
      axes
    };
    if (axes.x) {
      otherScroller.x = me.x;
    }
    if (axes.y) {
      otherScroller.y = me.y;
    }
    if (!otherScroller.isPartneredWith(me)) {
      otherScroller.addPartner(me, axes);
    }
  }
  eachPartner(fn2) {
    const { partners } = this;
    if (partners) {
      Object.values(partners).forEach(fn2);
    }
  }
  /**
   * Breaks the link between this Scroller and the passed Scroller set up by the
   * {@link #function-addPartner} method.
   * @param {Core.helper.util.Scroller} otherScroller The Scroller to unlink from.
   */
  removePartner(otherScroller) {
    if (otherScroller && this.isPartneredWith(otherScroller)) {
      delete this.partners[otherScroller.id];
      otherScroller.removePartner(this);
    }
  }
  isPartneredWith(otherScroller) {
    var _a5;
    return Boolean((_a5 = this.partners) == null ? void 0 : _a5[otherScroller.id]);
  }
  /**
   * Breaks the link between this Scroller and all other Scrollers set up by the
   * {@link #function-addPartner} method.
   * @internal
   */
  clearPartners() {
    if (this.partners) {
      Object.values(this.partners).forEach((otherScroller) => otherScroller.scroller.removePartner(this));
    }
  }
  /**
   * Scrolls the passed element or {@link Core.helper.util.Rectangle} into view according to the passed options.
   * @param {HTMLElement|Core.helper.util.Rectangle} element The element or a Rectangle in document space to scroll
   * into view.
   * @param {BryntumScrollOptions} [options] How to scroll.
   * @returns {Promise} A promise which is resolved when the element has been scrolled into view.
   */
  async scrollIntoView(element, options = defaultScrollOptions) {
    const me = this, { isRectangle } = element, originalRect = isRectangle ? element : Rectangle.from(element), { xDelta, yDelta } = me.getDeltaTo(element, options), result = me.scrollBy(xDelta, yDelta, options);
    if (options.highlight || options.focus) {
      result.then(() => {
        var _a5, _b2;
        element = isRectangle ? originalRect.translate(-xDelta, -yDelta) : (_b2 = (_a5 = options.elementAfterScroll) == null ? void 0 : _a5.call(options)) != null ? _b2 : element;
        if (options.highlight) {
          if (isNaN(options.highlight)) {
            (me.widget || me).callback(options.highlight, null, [element]);
          } else {
            DomHelper.highlight(element, me);
          }
        }
        if (options.focus) {
          element.focus({ preventScroll: true });
        }
      });
    }
    return result;
  }
  /**
   * Scrolls the passed element into view according to the passed options.
   * @param {HTMLElement} element The element in document space to scroll into view.
   * @param {BryntumScrollOptions} [options] How to scroll.
   * @returns {Promise} A promise which is resolved when the element has been scrolled into view.
   */
  static async scrollIntoView(element, options = defaultScrollOptions, rtl = false) {
    const target = Rectangle.from(element), animate = typeof options === "object" ? options.animate : options, globalScroller = _Scroller._globalScroller || (_Scroller._globalScroller = new _Scroller()), deltas = [];
    globalScroller.rtlSource = { rtl };
    let totalX = 0, totalY = 0, result;
    if (options.maxWidth) {
      target.width = Math.min(target.width, options.maxWidth);
      options.maxWidth = null;
    }
    if (options.maxHeight) {
      target.height = Math.min(target.height, options.maxHeight);
      options.maxHeight = null;
    }
    for (let ancestor = element.parentNode; ancestor.nodeType === Node.ELEMENT_NODE; ancestor = ancestor.parentNode) {
      if (ancestor === document.body && ancestor !== document.scrollingElement) {
        continue;
      }
      const style = ancestor === document.scrollingElement ? allScroll : ancestor.ownerDocument.defaultView.getComputedStyle(ancestor), s = DomDataStore.get(ancestor, "scroller");
      if (options.y !== false && ((s == null ? void 0 : s.hasOverflowY) || isScrollable[style.overflowY] && ancestor.scrollHeight > ancestor.clientHeight) || options.x !== false && ((s == null ? void 0 : s.hasOverflowX) || isScrollable[style.overflowX] && ancestor.scrollWidth > ancestor.clientWidth)) {
        let scroller = globalScroller;
        if (s) {
          scroller = s;
        } else {
          globalScroller.element = ancestor;
          globalScroller.positionDirty = true;
        }
        const { xDelta, yDelta } = scroller.getDeltaTo(target, options);
        if (xDelta || yDelta) {
          deltas.push({
            element: ancestor,
            x: ancestor.scrollLeft,
            y: ancestor.scrollTop,
            xDelta,
            yDelta
          });
          target.translate(-xDelta, -yDelta);
          totalX += xDelta;
          totalY += yDelta;
        }
      }
    }
    if (deltas.length) {
      const absX = Math.abs(totalX), absY = Math.abs(totalY);
      let duration = animate && (typeof animate === "number" ? animate : typeof animate.duration === "number" ? animate.duration : 300);
      if (duration && (absX > 10 || absY > 10)) {
        if (Math.max(absX, absY) < 50) {
          duration = Math.min(duration, 500);
        }
        result = globalScroller.scrollAnimation = FunctionHelper.animate(duration, (progress) => {
          const isEnd = progress === 1;
          for (const { element: element2, x, y, xDelta, yDelta } of deltas) {
            globalScroller.element = element2;
            if (xDelta) {
              globalScroller.x = Math[rtl ? "min" : "max"](x + (isEnd ? xDelta : Math.round(xDelta * progress)), 0);
            }
            if (yDelta) {
              globalScroller.y = Math.max(y + (isEnd ? yDelta : Math.round(yDelta * progress)), 0);
            }
          }
        }, null, animate.easing);
        result.then(() => {
          globalScroller.scrollAnimation = null;
        });
      } else if (absX >= 1 || absY >= 1) {
        for (const { element: element2, xDelta, yDelta } of deltas) {
          element2.scrollTop += yDelta;
          element2.scrollLeft += xDelta;
        }
        result = scrollPromise(deltas[deltas.length - 1].element);
      }
    }
    if (options.highlight || options.focus) {
      await result;
      if (options.highlight) {
        if (isNaN(options.highlight)) {
          globalScroller.callback(options.highlight, null, [element]);
        } else {
          DomHelper.highlight(element, globalScroller);
        }
      }
      if (options.focus) {
        element.focus({ preventScroll: true });
      }
    }
    return result;
  }
  /**
   * Scrolls by the passed deltas according to the passed options.
   * @param {Number} [xDelta=0] How far to scroll in the X axis.
   * @param {Number} [yDelta=0] How far to scroll in the Y axis.
   * @param {Object|Boolean} [options] How to scroll. May be passed as `true` to animate.
   * @param {Boolean} [options.silent] Set to `true` to suspend `scroll` events during scrolling.
   * @param {AnimateScrollOptions|Boolean|Number} [options.animate] Set to `true` to animate the scroll by 300ms,
   * or the number of milliseconds to animate over, or an animation config object.
   * @returns {Promise} A promise which is resolved when the scrolling has finished.
   */
  scrollBy(xDelta = 0, yDelta = 0, options = defaultScrollOptions) {
    const me = this, animate = typeof options === "object" ? options.animate : options, absX = Math.abs(xDelta), absY = Math.abs(yDelta);
    if (me.scrollAnimation) {
      me.scrollAnimation.cancel();
      me.scrollAnimation = null;
    }
    if (xDelta || yDelta) {
      me.silent = options.silent;
    }
    let duration = animate && (typeof animate === "number" ? animate : typeof animate.duration === "number" ? animate.duration : 300);
    if (duration && (absX > 10 || absY > 10)) {
      const { x, y } = me;
      let lastX = x, lastY = y;
      if (Math.max(absX, absY) < 50) {
        duration = Math.min(duration, 500);
      }
      me.scrollAnimation = FunctionHelper.animate(duration, (progress) => {
        var _a5, _b2;
        const isEnd = progress === 1;
        if (xDelta) {
          if (Math.abs(me.x - lastX) > 1 && !options.force) {
            return (_a5 = me.scrollAnimation) == null ? void 0 : _a5.cancel();
          }
          me.x = Math.max(x + (isEnd ? xDelta : Math.round(xDelta * progress)), 0);
        }
        if (yDelta) {
          if (Math.abs(me.y - lastY) > 1 && !options.force) {
            return (_b2 = me.scrollAnimation) == null ? void 0 : _b2.cancel();
          }
          me.y = Math.max(y + (isEnd ? yDelta : Math.round(yDelta * progress)), 0);
        }
        lastX = me.x;
        lastY = me.y;
      }, me, animate.easing);
      me.element.classList.add("b-scrolling");
      me.scrollAnimation.then(() => {
        if (!me.isDestroyed) {
          me.element.classList.remove("b-scrolling");
          me.scrollAnimation = null;
        }
      });
      return me.scrollAnimation;
    } else {
      if (xDelta || yDelta) {
        const xBefore = me.x, yBefore = me.y;
        if (Math.round(absX) >= 1) {
          me.x += xDelta;
        }
        if (Math.round(absY) >= 1) {
          me.y += yDelta;
        }
        if (me.x !== xBefore || me.y !== yBefore) {
          return scrollPromise(me.element);
        }
      }
      return immediatePromise4;
    }
  }
  /**
   * Scrolls to the passed position according to the passed options.
   * @param {Number} [toX=0] Where to scroll to in the X axis.
   * @param {Number} [toY=0] Where to scroll to in the Y axis.
   * @param {Object|Boolean} [options] How to scroll. May be passed as `true` to animate.
   * @param {AnimateScrollOptions|Boolean|Number} [options.animate] Set to `true` to animate the scroll by 300ms,
   * or the number of milliseconds to animate over, or an animation config object.
   * @returns {Promise} A promise which is resolved when the scrolling has finished.
   */
  async scrollTo(toX, toY, options) {
    const { x, y } = this, xDelta = toX == null ? 0 : toX - x, yDelta = toY == null ? 0 : toY - y;
    this.scrollingToCenter = options == null ? void 0 : options.scrollingToCenter;
    return this.scrollBy(xDelta, yDelta, options);
  }
  doDestroy() {
    var _a5, _b2;
    const me = this;
    if (me._element) {
      me._element.removeEventListener("scroll", me.scrollHandler);
      (_a5 = me.wheelListenerRemover) == null ? void 0 : _a5.call(me);
      if (me._contentElement) {
        ResizeMonitor.removeResizeListener(me.contentElement, me.resizeHandler);
      }
      ResizeMonitor.removeResizeListener(me._element, me.resizeHandler);
    }
    (_b2 = me.scrollAnimation) == null ? void 0 : _b2.cancel();
    Object.values(me.partners || {}).forEach(({ scroller }) => scroller.removePartner(me));
    super.doDestroy();
  }
  /**
   * Respond to style changes to monitor scroll *when this Scroller is in `translate: true` mode.*
   * @param {Object[]} mutations The ElementMutation records.
   * @private
   */
  onElMutation(mutations) {
    const me = this, [x, y] = DomHelper.getTranslateXY(me.element);
    if (me._x !== -x || me.y !== -y) {
      const scrollEvent = new CustomEvent("scroll", { bubbles: true });
      Object.defineProperty(scrollEvent, "target", {
        get: () => me.element
      });
      me.onScroll(scrollEvent);
    }
  }
  onElResize() {
    const me = this, { widget } = me;
    if (!(widget == null ? void 0 : widget.isAnimating)) {
      me.syncOverflowState();
    } else if (widget.findListener("animationend", "onElResize", me) === -1) {
      widget.ion({
        animationEnd: "onElResize",
        thisObj: me,
        once: true
      });
    }
  }
  onScroll(e) {
    var _a5;
    const me = this, { element } = me;
    if (!((_a5 = me.widget) == null ? void 0 : _a5.isDestroyed)) {
      me.positionDirty = true;
      if (!element.classList.contains("b-scrolling")) {
        element.classList.add("b-scrolling");
      }
      e.widget = me.widget;
      e.scrollingToCenter = me.scrollingToCenter;
      if (!me.silent) {
        me.trigger("scroll", e);
      }
      me.syncPartners();
      me.controllingPartner = null;
      me.onScrollEnd(e);
    }
  }
  /**
   * Syncs all attached scrolling partners with the scroll state of this Scroller.
   * @param {Boolean} force Allow this to sync a partner which is controlling this via a sync.
   * @param {Boolean} [propagate] Propagate any change immediately onwards through
   * further linked partners immediately rather than waiting for our own scroll event.
   * @internal
   */
  syncPartners(force, propagate = this.propagateSync) {
    const me = this;
    if (me.partners) {
      Object.values(me.partners).forEach(({ axes, scroller }) => {
        if (scroller !== me.controllingPartner || force) {
          if (scroller.sync(me, axes) && propagate) {
            scroller.syncPartners(force, propagate);
          }
        }
      });
    }
  }
  onScrollEnd(e) {
    const me = this;
    if (me.silent) {
      me.silent = false;
    }
    me.trigger("scrollEnd", e);
    me.scrollingToCenter = false;
    me.controllingPartner = null;
    me.element.classList.remove("b-scrolling");
  }
  /**
   * Returns the xDelta and yDelta values in an object from the current scroll position to the
   * passed element or Rectangle.
   * @param {HTMLElement|Core.helper.util.Rectangle} element The element or a Rectangle to calculate deltas for.
   * @param {Object} [options] How to scroll.
   * @param {'start'|'end'|'center'|'nearest'} [options.block] How to align the element in the block direction (y axis).
   * @param {'start'|'end'|'center'|'nearest'} [options.inline] How to align the element in the inline direction (x axis).
   * @param {Number} [options.edgeOffset] A margin around the element or rectangle to bring into view.
   * @param {Boolean} [options.x] Pass as `false` to disable scrolling in the `X` axis.
   * @param {Boolean} [options.y] Pass as `false` to disable scrolling in the `Y` axis.
   * @param {Number} [options.maxWidth] How much of the target's width to bring into view if the target is wider.
   * @param {Number} [options.maxHeight] How much of the target's height to bring into view if the target is taller.
   * @param {Boolean} [options.constrainToScrollable=true] Pass as `false` to allow offsets beyond the available scroll range.
   * @param {Boolean} [options.precise] If `true`, return a precise delta, otherwise round it.
   * @param {Core.helper.util.Rectangle} [options.viewport] The scrolling viewport rectangle if it's not the element itself.
   * @returns {Object} `{ xDelta, yDelta }`
   * @internal
   */
  getDeltaTo(element, options) {
    const me = this, {
      x,
      y
    } = me, {
      precise
    } = options, viewport = options.viewport || me.viewport;
    if (!viewport) {
      return {
        xDelta: 0,
        yDelta: 0
      };
    }
    const edgeOffset = normalizeEdgeOffset(options.edgeOffset), targetRect = element instanceof Rectangle ? element : Rectangle.from(element);
    let xDelta = options.x === false ? 0 : me.getDelta({
      viewport,
      targetRect,
      edgeOffset: edgeOffset.inline,
      flow: "inline",
      targetEdge: options.inline || options.block || "nearest",
      maxSize: options.maxWidth,
      precise
    }) * (me.isRTL ? -1 : 1), yDelta = options.y === false ? 0 : me.getDelta({
      viewport,
      targetRect,
      edgeOffset: edgeOffset.block,
      flow: "block",
      targetEdge: options.block || "nearest",
      maxSize: options.maxHeight,
      precise
    });
    if (options.constrainToScrollable !== false) {
      xDelta = Math.max(Math.min(xDelta, Math.max(me.maxX - x, 0)), -x);
      yDelta = Math.max(Math.min(yDelta, Math.max(me.maxY - y, 0)), -y);
    }
    return {
      xDelta,
      yDelta
    };
  }
  /**
   * Returns the delta to scroll the target rectangle into view in the specified flow direction, "inline" or "block".
   * @param {Object} options
   * @param {Core.helper.util.Rectangle} options.viewport The viewport rectangle.
   * @param {Core.helper.util.Rectangle} options.targetRect The target rectangle.
   * @param {Object} [options.edgeOffset] A margin around the element or rectangle to bring into view.
   * @param {'block'|'inline'} [options.flow] Which dimension we are calculating.
   * @param {'start'|'end'|'center'|'nearest'} [options.targetEdge] How to align the element within the scrolling viewport.
   * @param {Number} [options.maxSize] The maximum size of the target rectangle.
   * @param {Boolean} [options.precise] If `true`, return a precise delta, otherwise round it.
   * @returns {Number}
   * @internal
   */
  getDelta({
    viewport,
    targetRect,
    edgeOffset = { start: 0, end: 0 },
    // { start, end }
    flow,
    // 'block'|'inline'
    targetEdge = "nearest",
    // 'start'|'end'|'center'|'nearest',
    maxSize,
    precise
  }) {
    const isBlock = flow === "block", isRTL = !isBlock && this.isRTL, axis = isBlock ? "y" : "x", axisEnd = isBlock ? "bottom" : "right", startEdge = isBlock ? "y" : isRTL ? "right" : "x", endEdge = isBlock ? "bottom" : isRTL ? "x" : "right", dimension = isBlock ? "height" : "width";
    let delta = 0;
    if (targetEdge === "center") {
      delta = targetRect.center[axis] - viewport.center[axis];
    } else if (targetEdge === "start" || targetEdge === "end") {
      const isStart = targetEdge === "start", edge = isStart ? startEdge : endEdge, margin = edgeOffset[targetEdge], edgeIncrement = viewport[dimension] > margin ? (isStart ? -1 : 1) * (isBlock || !isRTL ? 1 : -1) : 0;
      delta = targetRect[edge] + margin * edgeIncrement - viewport[edge];
    } else {
      const s = Math.max(targetRect[axis], viewport[axis]), e = Math.min(targetRect[axisEnd], viewport[axisEnd]), intersectionSize = e > s ? e - s : 0, targetSize = targetRect[dimension];
      if (intersectionSize <= Math.min(maxSize || Number.MAX_SAFE_INTEGER, targetSize) && targetSize > intersectionSize) {
        targetRect = targetRect.clone();
        if (targetRect[dimension] > maxSize) {
          if (targetRect[axis] < viewport[axis]) {
            targetRect[axis] = Math.max(targetRect[axis], targetRect[axisEnd] - maxSize);
          } else {
            targetRect[dimension] = Math.min(targetSize, maxSize);
          }
          edgeOffset = void 0;
        }
        if (Math.min(targetRect[dimension], maxSize || Number.MAX_SAFE_INTEGER) > viewport[dimension]) {
          targetEdge = "start";
        } else {
          targetEdge = Math.abs(targetRect[startEdge] - viewport[startEdge]) < Math.abs(targetRect[endEdge] - viewport[endEdge]) ? "start" : "end";
        }
        delta = this.getDelta({
          viewport,
          targetRect,
          edgeOffset,
          flow,
          targetEdge,
          maxSize,
          precise
        });
      }
    }
    return precise ? delta : Math.round(delta);
  }
  /**
   * A {@link Core/helper/util/Rectangle} describing the bounds of the scrolling viewport.
   * @property {Core.helper.util.Rectangle}
   */
  get viewport() {
    const stickies = this.stickies ? ArrayHelper.asArray(this.stickies).flatMap((e) => typeof e === "string" ? Array.from(this.element.querySelectorAll(e)) : e) : null, result = Rectangle.client(this.element);
    return stickies ? result.exclude(stickies.map((e) => Rectangle.from(e))) : result;
  }
  updateWidget(widget) {
    this.rtlSource = this.owner = widget;
  }
  updateElement(element, oldElement) {
    var _a5;
    const me = this;
    if (me === _Scroller._globalScroller) {
      me._element = element;
      me.positionDirty = true;
      return;
    }
    const scrollHandler = me.scrollHandler || (me.scrollHandler = me.onScroll.bind(me)), resizeHandler = me.resizeHandler || (me.resizeHandler = me.onElResize.bind(me));
    if (oldElement) {
      DomDataStore.remove(oldElement, "scroller");
      if (me.translate) {
        (_a5 = me.mutationObserver) == null ? void 0 : _a5.disconnect();
      } else {
        oldElement.removeEventListener("scroll", scrollHandler);
        oldElement.classList.remove(scrollerCls);
        oldElement.style.overflowX = oldElement.style.overflowY = "";
      }
      ResizeMonitor.removeResizeListener(oldElement, resizeHandler);
    }
    if (element) {
      DomDataStore.set(element, "scroller", me);
      if (me.translate) {
        if (!me.mutationObserver) {
          me.mutationObserver = new MutationObserver(me.mutationHandler || (me.mutationHandler = me.onElMutation.bind(me)));
        }
        me._x = me._y = 0;
        if (document.contains(element)) {
          const [x, y] = DomHelper.getTranslateXY(element);
          me._x = -x;
          me._y = -y;
        }
        me.mutationObserver.observe(element, { attributes: true });
      } else {
        element.addEventListener("scroll", scrollHandler);
        element.classList.add(scrollerCls);
      }
      ResizeMonitor.addResizeListener(element, resizeHandler);
      if (!me.widget) {
        me.rtlSource = {
          get rtl() {
            return DomHelper.getStyleValue(element, "direction") === "rtl";
          }
        };
      }
      if (me.isRTL) {
        element.classList.add("b-rtl");
      }
      if (me.positionDirty) {
        me.updateOverflowX(me.overflowX);
        me.updateOverflowY(me.overflowY);
      }
      me.syncOverflowState();
      if (me.isConfiguring) {
        me._x && me.updateX(me._x);
        me._y && me.updateY(me._y);
      }
    }
    me.positionDirty = true;
  }
  get unsynced() {
    const me = this, [x, y] = me.xy;
    return me._x !== x || me._y !== y;
  }
  get xy() {
    const { element } = this;
    let x, y;
    if (element) {
      if (this.translate) {
        [x, y] = DomHelper.getTranslateXY(element);
        x = -x;
        y = -y;
      } else {
        x = Math.abs(element.scrollLeft);
        y = element.scrollTop;
      }
    }
    return [x, y];
  }
  refreshPosition() {
    const me = this, [x, y] = me.xy;
    me._x = x;
    me._y = y;
    me.positionDirty = false;
  }
  fixPositionSync() {
    const me = this, { element, xy: xy2, _x, _y } = me;
    if (!me.positionDirty && !(_x === xy2[0] && _y === xy2[1])) {
      if (me.translate) {
        DomHelper.setTranslateX(element, -_x);
        DomHelper.setTranslateY(element, -_y);
      } else {
        element.scrollTo({
          behavior: "instant",
          left: me.isRTL ? -_x : _x,
          top: _y
        });
      }
    }
  }
  /**
   * The horizontal scroll position of the widget.
   *
   * Note that this is always positive. Horizontal scrolling using the `X` property always proceeds
   * in the positive direction.
   *
   * @property {Number}
   */
  get x() {
    this.positionDirty && this.refreshPosition();
    return this._x;
  }
  /**
   * The natural DOM horizontal scroll position of the widget.
   *
   * Note that this proceeds from 0 into negative space in RTL mode.
   *
   * @property {Number}
   */
  get scrollLeft() {
    return this.x * (this.isRTL ? -1 : 1);
  }
  changeX(x) {
    x = Math.max(x, 0);
    if (!this.isConfiguring || x) {
      return x;
    }
    this._x = x;
  }
  updateContentElement(contentElement) {
    if (contentElement) {
      contentElement = typeof contentElement === "string" ? this.element.querySelector(contentElement) : contentElement;
      ResizeMonitor.addResizeListener(contentElement, this.resizeHandler);
    }
  }
  updateX(x) {
    var _a5;
    const me = this, { behavior, element } = me;
    if (element && !((_a5 = me.widget) == null ? void 0 : _a5.isConfiguring)) {
      x = Math.round(x);
      me.trigger("scrollStart", { x });
      if (me.translate) {
        DomHelper.setTranslateX(element, -x);
      } else if (behavior) {
        element.scrollTo({
          behavior,
          left: me.isRTL ? -x : x
        });
      } else {
        element.scrollLeft = me.isRTL ? -x : x;
      }
    }
    me.positionDirty = true;
  }
  /**
   * Syncs this Scroller with the passed Scroller in the passed axes.
   * @param {Core.helper.util.Scroller} controllingPartner The Scroller which is dictating our new scroll position.
   * @param {Object} axes `{x : <boolean>, y : <boolean> }` which axes to sync.
   * @param {Boolean} axes.x Sync horizontal scroll.
   * @param {Boolean} axes.y Sync vertical scroll.
   * @returns {Boolean} `true` if this Scroller needed the passed axes syncing, `false`
   * if no changes were made.
   * @internal
   */
  sync(controllingPartner, axes) {
    const me = this, { x, y } = axes;
    let result = false;
    if (x) {
      if (me.x !== controllingPartner.x) {
        me.controllingPartner = controllingPartner;
        me.x = controllingPartner.x;
        result = true;
      }
    }
    if (y) {
      if (me.y !== controllingPartner.y) {
        me.controllingPartner = controllingPartner;
        me.y = controllingPartner.y;
        result = true;
      }
    }
    return result;
  }
  /**
   * The vertical scroll position of the widget.
   * @property {Number}
   */
  get y() {
    this.positionDirty && this.refreshPosition();
    return this._y;
  }
  changeY(y) {
    if (!this.isConfiguring || y) {
      return y;
    }
    this._y = y;
  }
  updateY(y) {
    const me = this, { behavior, element, widget } = me;
    if (element && !(widget == null ? void 0 : widget.isConfiguring)) {
      me.trigger("scrollStart", { y });
      if (me.translate) {
        DomHelper.setTranslateY(element, -y);
      } else if (behavior) {
        element.scrollTo({
          behavior,
          top: y
        });
      } else {
        element.scrollTop = y;
      }
    }
    me.positionDirty = true;
  }
  /**
   * The maximum `X` scrollable position of the widget.
   * @property {Number}
   * @readonly
   */
  get maxX() {
    return this.scrollWidth - this.clientWidth;
  }
  /**
   * The maximum `Y` scrollable position of the widget.
   * @property {Number}
   * @readonly
   */
  get maxY() {
    return this.scrollHeight - this.clientHeight;
  }
  /**
   * The furthest possible `scrollLeft` position of the widget. Will be negative
   * if in writing direction is RTL.
   * @property {Number}
   * @readonly
   */
  get lastScrollLeft() {
    return (this.scrollWidth - this.clientWidth) * (this.isRTL ? -1 : 1);
  }
  updateOverflowX(overflowX, oldOverflowX) {
    const me = this, { element, translate } = me, { style, classList } = element;
    if (oldOverflowX === "hidden-scroll") {
      classList.remove("b-hide-scroll");
    }
    if (overflowX === "hidden-scroll" && !translate) {
      const otherAxisScrollable = isScrollable[me.overflowY];
      if (otherAxisScrollable) {
        overflowX = "hidden";
        me.enableWheel();
      } else {
        classList.add("b-hide-scroll");
      }
    }
    if (!translate) {
      style.overflowX = scrollLiterals[overflowX] || overflowX;
    }
    if (!me.isConfiguring) {
      me.positionDirty = true;
      me.syncOverflowState();
    }
  }
  updateOverflowY(overflowY, oldOverflowY) {
    const me = this, { element, translate } = me, { style, classList } = element;
    if (oldOverflowY === "hidden-scroll") {
      classList.remove("b-hide-scroll");
    }
    if (overflowY === "hidden-scroll" && !translate) {
      const otherAxisScrollable = isScrollable[me.overflowX];
      if (otherAxisScrollable) {
        overflowY = "hidden";
        me.enableWheel();
      } else {
        classList.add("b-hide-scroll");
      }
    }
    if (!translate) {
      style.overflowY = scrollLiterals[overflowY] || overflowY;
    }
    if (!me.isConfiguring) {
      me.positionDirty = true;
      me.syncOverflowState();
    }
  }
  enableWheel() {
    if (!this.wheelListenerRemover) {
      this.wheelListenerRemover = EventHelper.on({
        element: this.element,
        wheel: "onWheel",
        thisObj: this
      });
    }
  }
  onWheel(e) {
    const me = this, { overflowX, overflowY } = me, dx = e.deltaX, dy = e.deltaY, delta = Math.abs(dx) - Math.abs(dy);
    if (overflowX === "hidden-scroll" && isScrollable[overflowY] && delta > 0) {
      me.x += dx;
    } else if (overflowY === "hidden-scroll" && isScrollable[overflowX] && delta < 0) {
      me.y += dy;
    }
  }
  /**
   * The horizontal scroll range of the widget.
   * @property {Number}
   * @readonly
   */
  get scrollWidth() {
    var _a5, _b2;
    return (_b2 = (_a5 = this.element) == null ? void 0 : _a5.scrollWidth) != null ? _b2 : 0;
  }
  set scrollWidth(scrollWidth) {
    const me = this, { element, isRTL } = me;
    let stretcher = me.widthStretcher;
    if (stretcher && scrollWidth == null) {
      stretcher.remove();
      me.widthStretcher = null;
    } else if (scrollWidth) {
      scrollWidth = Math.abs(scrollWidth);
      if (!stretcher) {
        stretcher = me.widthStretcher = DomHelper.createElement({
          className: "b-scroller-stretcher b-horizontal-stretcher",
          // Avoid the default role="presentation" which is invalid here
          role: null,
          // Should survive its surroundings being DomSynced
          retainElement: true
        });
      }
      stretcher.style.transform = `translateX(${(scrollWidth - 1) * (isRTL ? -1 : 1)}px)`;
      if (element && !element.contains(stretcher)) {
        element.insertBefore(stretcher, element.firstElementChild);
      }
    }
    element.style.setProperty("--scroll-width", DomHelper.setLength(scrollWidth));
    if (me.propagate !== false) {
      me.eachPartner(({ scroller }) => {
        scroller.propagate = false;
        scroller.scrollWidth = scrollWidth;
        delete scroller.propagate;
      });
    }
    me.positionDirty = true;
    me.syncOverflowState();
  }
  get scrollHeight() {
    var _a5, _b2;
    return (_b2 = (_a5 = this.element) == null ? void 0 : _a5.scrollHeight) != null ? _b2 : 0;
  }
  /**
   * The vertical scroll range of the widget. May be set to larger than the actual data
   * height to enable virtual scrolling. This is how the grid extends its scroll range
   * while only rendering a small subset of the dataset.
   * @property {Number}
   */
  set scrollHeight(scrollHeight) {
    const me = this, stretcher = me.stretcher || (me.stretcher = DomHelper.createElement({
      className: "b-scroller-stretcher",
      // Avoid the default role="presentation" which is invalid here
      role: null
    }));
    stretcher.style.transform = `translateY(${scrollHeight - 1}px)`;
    if (me.element && me.element.lastChild !== stretcher) {
      me.element.appendChild(stretcher);
    }
    me.positionDirty = true;
    me.syncOverflowState();
  }
  /**
   * The client width of the widget.
   * @property {Number}
   * @readonly
   */
  get clientWidth() {
    var _a5;
    return ((_a5 = this.element) == null ? void 0 : _a5.clientWidth) || 0;
  }
  /**
   * The client height of the widget.
   * @property {Number}
   * @readonly
   */
  get clientHeight() {
    var _a5;
    return ((_a5 = this.element) == null ? void 0 : _a5.clientHeight) || 0;
  }
  /**
   * The unique ID of this Scroller
   * @property {String}
   * @readonly
   */
  get id() {
    if (!this._id) {
      this._id = IdHelper.generateId(this.widget ? `${this.widget.id}-` : "scroller-");
    }
    return this._id;
  }
  //region Extract configs
  // This function is not meant to be called by any code other than Base#getCurrentConfig().
  preProcessCurrentConfigs(configs) {
    super.preProcessCurrentConfigs();
    delete configs.widget;
    delete configs.element;
  }
  //endregion
};
Scroller._$name = "Scroller";

// ../Core/lib/Core/widget/mixin/KeyMap.js
var regexpRE = /\/[^/]+\//;
var keyMapRegexes = {};
var KeyMap_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    get widgetClass() {
    }
    /**
     * Override to attach the keyMap keydown event listener to something else than this.element
     * @private
     */
    get keyMapElement() {
      return this.element;
    }
    /**
     * Override to make keyMap resolve subcomponent actions to something else than this.features.
     * @private
     */
    get keyMapSubComponents() {
      return this.features;
    }
    /**
     * Add a key binding to this widget's keyMap.
     *
     * ```javascript
     * // Add a key binding to the widget to call its own onCtrlEnter method when Ctrl+Enter is pressed.
     * myWidget.addKeyBinding('Ctrl+Enter', 'onCtrlEnter');
     * ```
     * @param {String} key The key combination to bind the action to. The key combination is the key name
     * prefixed by any modifiers separated by `+` characters. The key names are case-insensitive. The following
     * modifiers are supported:
     * - `'Alt'`
     * - `'Ctrl'`
     * - `'Meta'`
     * - `'Shift'`
     * @param {String|Function} action The function, or name of the instance method to call when the key combination is
     * received.
     * @internal
     */
    addKeyBinding(key, action) {
      const keyMap = this.keyMap, keyDef = this.mergeKeyMaps({ [key]: keyMap == null ? void 0 : keyMap[key] }, { [key]: action })[key];
      if (keyMap) {
        keyMap[key] = keyDef;
      } else {
        this.keyMap = { [key]: keyDef };
      }
    }
    /**
     * Remove a key binding to this widget's keyMap.
     *
     * ```javascript
     * // Add a key binding to the widget to call its own onCtrlEnter method when Ctrl+Enter is pressed.
     * myWidget.removeKeyBinding('Ctrl+Enter', 'onCtrlEnter');
     * ```
     * @param {String|Function} action The function, or name of the instance method to call when the key combination is
     * received.
     * @internal
     */
    removeKeyBinding(key, action) {
      var _a6;
      const keyMapEntry = (_a6 = this.keyMap) == null ? void 0 : _a6[key];
      if (keyMapEntry) {
        let index = 0;
        if (Array.isArray(keyMapEntry)) {
          index = keyMapEntry.findIndex((entry) => entry === action || entry.handler === action);
          if (index > -1) {
            keyMapEntry.splice(index, 1);
          }
        } else if (keyMapEntry === action || keyMapEntry.handler === action) {
          delete this.keyMap[key];
        }
      }
    }
    /**
     * Returns the `keyMap` property name which matches the passed KeyboardEvent if any.
     * @param {KeyboardEvent} keyEvent
     * @param {Object} [keyMap=this.keyMap]
     * @returns {String} the key into the `keyMap` matched by the passed KeyboardEvent
     * @internal
     */
    matchKeyMapEntry(keyEvent, keyMap = this.keyMap) {
      const { input } = this, { altKey, ctrlKey, metaKey, shiftKey, target } = keyEvent;
      if (keyEvent.key.length === 1 && !(altKey || ctrlKey || metaKey || shiftKey) && !(input && keyEvent.composedPath().includes(input)) && DomHelper.isEditable(target, true)) {
        return true;
      }
      if (keyMap && !keyEvent.handled && keyEvent.key !== void 0) {
        return ObjectHelper.keys(keyMap).find((keyString) => {
          const keys = keyString === "+" ? [] : keyString.toLowerCase().split("+"), last = keyString.endsWith("+") ? "+" : keys.pop(), actualKey = last === "space" ? " " : last, requireAlt = keys.includes("alt"), requireCtrl = keys.includes("ctrl"), requireMeta = keys.includes("meta"), requireShift = keys.includes("shift"), regExp = regexpRE.test(actualKey) && (keyMapRegexes[actualKey] || (keyMapRegexes[actualKey] = new RegExp(actualKey.substring(1, actualKey.length - 1)))), matchesKey = regExp ? regExp.test(keyEvent.key.toLowerCase()) : actualKey === keyEvent.key.toLowerCase();
          return matchesKey && (requireAlt ? altKey : !altKey) && (requireCtrl ? ctrlKey : !ctrlKey) && (requireMeta ? metaKey : !metaKey) && (requireShift ? shiftKey : !shiftKey);
        });
      }
    }
    /**
     * Called on keyMapElement keyDown
     * @private
     */
    async performKeyMapAction(event) {
      var _a6, _b2, _c2;
      const me = this, { keyMap } = me;
      let actionHandled = false, action, candidate, delegate, preventDefault4;
      if (keyMap && !event.handled && event.key !== void 0) {
        const key = me.matchKeyMapEntry(event);
        if (keyMap[key]) {
          const actions5 = ArrayHelper.asArray(keyMap[key]);
          event.fromKeyMap = true;
          event.widget = this;
          for (action of actions5) {
            preventDefault4 = true;
            if (ObjectHelper.isObject(action)) {
              if (!action.handler) {
                continue;
              }
              if (action.preventDefault === false) {
                preventDefault4 = false;
              }
              action = action.handler;
            }
            if (typeof action === "string") {
              const {
                thisObj,
                handler
              } = me.resolveKeyMapAction(action);
              if (((_a6 = thisObj.isActionAvailable) == null ? void 0 : _a6.call(thisObj, { key, action, event, actionName: action.split(".").pop() })) !== false) {
                let result = handler.call(thisObj, event);
                if (ObjectHelper.isPromise(result)) {
                  result = await result;
                }
                if (result !== false) {
                  actionHandled = true;
                  break;
                }
              }
            } else if (action.call(me, event) !== false) {
              actionHandled = true;
              break;
            }
          }
          if (actionHandled) {
            if (preventDefault4) {
              event.preventDefault();
            }
            event.handled = true;
          }
        }
        if (!actionHandled) {
          if (delegate = ObjectHelper.getTruthyKeys(keyMap.delegate)) {
            for (candidate of delegate) {
              candidate = ObjectHelper.getPath(me, candidate);
              actionHandled = (_b2 = candidate == null ? void 0 : candidate.performKeyMapAction) == null ? void 0 : _b2.call(candidate, event);
              if (ObjectHelper.isPromise(actionHandled)) {
                actionHandled = await actionHandled;
              }
              if (actionHandled) {
                break;
              }
            }
          }
          if (!actionHandled) {
            (_c2 = me.defaultKeyHandler) == null ? void 0 : _c2.call(me, event);
          }
        }
      }
      return actionHandled;
    }
    /**
     * Resolves correct `this` and handler function.
     * If subComponent (action includes a dot) it will resolve in keyMapSubComponents (defaults to this.features).
     *
     * For example, in feature configurable:
     * `keyMap: {
     *     ArrowUp: 'navigateUp'
     * }`
     *
     * Will be translated (by InstancePlugin) to:
     * `keyMap: {
     *     ArrowUp: 'featureName.navigateUp'
     * }
     *
     * And resolved to correct function path here.
     *
     * Override to change action function mapping.
     * @private
     */
    resolveKeyMapAction(action) {
      const me = this, { keyMapSubComponents } = me;
      if (action.startsWith("up.") || action.startsWith("this.")) {
        return me.resolveCallback(action);
      }
      if (keyMapSubComponents && action.includes(".")) {
        const [component, actionName] = action.split(".");
        if (component && actionName) {
          return {
            thisObj: keyMapSubComponents[component],
            handler: keyMapSubComponents[component][actionName]
          };
        }
      }
      return {
        thisObj: me,
        handler: me[action]
      };
    }
    updateKeyMap(keyMap) {
      var _a6;
      const me = this, keys = ObjectHelper.keys(keyMap);
      (_a6 = me.keyMapDetacher) == null ? void 0 : _a6.call(me);
      me.keyMapDetacher = (keys.length || null) && EventHelper.on({
        element: me.keyMapElement,
        keydown: "keyMapOnKeyDown",
        thisObj: me
      });
    }
    // Hook on to this to catch keydown before keymap does
    keyMapOnKeyDown(event) {
      this.performKeyMapAction(event);
    }
    /**
     * This function is used for merging two keyMaps with each other. It can be used for example by a Grid's feature to
     * merge the fetature's keyMap into the Grid's with the use of a subPrefix.
     * @param {Object} target - The existing keyMap.
     * @param {Object} source - The keyMap we want to merge into target.
     * @param {Object} subPrefix - If keyMap actions in source should be prefixed, the prefix should be provided here.
     * As example, the prefix * `rowCopyPaste` will give the action 'rowCopyPaste.action'.
     * @private
     */
    mergeKeyMaps(target, source, subPrefix = null) {
      const mergedKeyMap = {};
      if (target) {
        ObjectHelper.assign(mergedKeyMap, target);
      }
      for (const key in source) {
        if (!source[key]) {
          continue;
        }
        const existingActions = ArrayHelper.asArray(target == null ? void 0 : target[key]), actions5 = [];
        if (existingActions == null ? void 0 : existingActions.length) {
          actions5.push(...existingActions);
        }
        if (!(existingActions == null ? void 0 : existingActions.some((a) => {
          const handler = a.handler ? a.handler : a;
          return typeof handler === "string" && handler.startsWith(subPrefix + ".");
        }))) {
          for (const action of ArrayHelper.asArray(source[key])) {
            if (ObjectHelper.isObject(action) && action.handler) {
              actions5.push(ObjectHelper.assignIf({
                handler: (subPrefix ? subPrefix + "." : "") + action.handler
              }, action));
            } else if (typeof action === "function") {
              actions5.push(action);
            } else {
              actions5.push((subPrefix ? subPrefix + "." : "") + action);
            }
          }
          actions5.sort((a, b) => {
            const weight = (a.weight || 0) - (b.weight || 0);
            if (weight === 0 && (existingActions == null ? void 0 : existingActions.length)) {
              return existingActions.indexOf(a) - existingActions.indexOf(b);
            }
            return weight;
          });
        }
        mergedKeyMap[key] = actions5;
      }
      return mergedKeyMap;
    }
  }, __publicField(_a5, "$name", "KeyMap"), __publicField(_a5, "configurable", {
    /**
     * An object whose keys are the [key](https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key) name
     * and optional modifier prefixes: `'Ctrl+'`, `'Alt+'`, `'Meta+'`, and `'Shift+'` (case-insensitive). The values
     * are the name of the instance method to call when the keystroke is received.
     *
     * For example:
     *
     * ```javascript
     *  keyMap : {
     *      'Ctrl+A': 'onSelectAll',
     *      'Ctrl+Z': 'onUndo',
     *      't'     : 'gotoNow',
     *      [/\d/]  : 'onDigit'
     *  }
     * ```
     *
     * If a key is a visible character (as above), and *not* modified by any modifier keys, and emanates from an editable
     * element (such as an `<input>` or `<textarea>`), the keystroke is ignored. This is to allow the browser to
     * handle typing in editable elements.
     *
     * In addition to key names, the special key `'delegate'` can be included to specify additional objects that
     * have their own `keyMap`. If a keystroke is not handled by this `keyMap`, the delegates are processed until
     * one has a matching entry in its `keyMap`. The value of the `delegate` key can be a single string, an array of
     * strings or an object whose {@link Core.helper.ObjectHelper#function-getTruthyKeys-static truthy keys} are
     * {@link Core.helper.ObjectHelper#function-getPath-static dot paths} identifying the delegate(s) related to
     * this instance.
     *
     * For example:
     *
     * ```javascript
     *  keyMap : {
     *      delegate : ['widgetMap.foo', 'widgetMap.bar', 'tbar']
     *  }
     * ```
     * An widget with the above `keyMap` will delegate to the child widgets named `foo` and `bar` via the widget's
     * {@link Core.widget.Container#property-widgetMap}. In addition, the `tbar` property of the widget will also
     * be considered as a delegate.
     *
     * The following is equivalent to the above `delegate` but using an object. This form can be used to allow
     * removal of entries by derived classes or an overriding instance config.
     *
     * ```javascript
     *  keyMap : {
     *      delegate : {
     *          'widgetMap.foo' : true,
     *          'widgetMap.bar' : true,
     *          tbar            : true
     *      }
     *  }
     * ```
     *
     * @prp {Object<String,KeyMapConfig>}
     * @category Accessibility
     */
    keyMap: {
      value: null,
      $config: {
        merge: "objects",
        nullify: true
      }
    }
  }), _a5;
};

// ../Core/lib/Core/widget/mixin/RTL.js
var RTL_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    get widgetClass() {
    }
    // Replace generated is-property, to reduce risk of confusion
    get isRTL() {
      return this.rtl;
    }
    updateRtl(rtl) {
      const { element } = this;
      if (element) {
        element.classList.toggle("b-rtl", rtl === true);
        element.classList.toggle("b-ltr", rtl === false);
      }
    }
    startConfigure(config) {
      var _a6, _b2;
      (_a6 = super.startConfigure) == null ? void 0 : _a6.call(this, arguments);
      if (!config.floating && config.rtl == null) {
        const me = this, el = config.rootElement || config.forElement || ((_b2 = me.parent) == null ? void 0 : _b2.contentElement) || (me.floating ? me.floatRoot : me.changeElementRef(me.getRenderContext(config)[0] || config.adopt));
        if (el) {
          config.rtl = el.nodeType === 1 && getComputedStyle(el).getPropertyValue("direction") === "rtl";
        } else {
          const owner = config.owner || config.parent;
          if (owner) {
            config.rtl = owner[owner.isConfiguring ? "peekConfig" : "getConfig"]("rtl");
          } else {
            config.rtl = getComputedStyle(document.body).getPropertyValue("direction") === "rtl";
          }
        }
        if (config.rtl) {
          me.configDone.rtl = false;
        }
      }
    }
    // Render is only called on outer widgets, children read their setting from their owner unless explicitly set
    render(...args) {
      var _a6, _b2, _c2;
      (_a6 = super.render) == null ? void 0 : _a6.call(this, ...args);
      if (BrowserHelper.isChrome && BrowserHelper.chromeVersion < 87 || BrowserHelper.isFirefox && BrowserHelper.firefoxVersion < 66 || BrowserHelper.isSafari && BrowserHelper.safariVersion < 14.1) {
        this.element.classList.add("b-legacy-inset");
      }
      if (getComputedStyle(this.element).direction === "rtl" || ((_b2 = this.owner) == null ? void 0 : _b2.rtl)) {
        this.rtl = true;
        (_c2 = this.childItems) == null ? void 0 : _c2.forEach((i) => i.rtl = true);
      }
    }
  }, __publicField(_a5, "$name", "RTL"), __publicField(_a5, "configurable", {
    /**
     * If a widget is rendered into an element which has computed style `direction:rtl`, this property will be
     * set to `true`
     *
     * Rendering a widget into an element which, either by a CSS rule, or by its inline `style` has an
     * explicit direction will cause the widget to use that direction regardless of the owning document's
     * direction.
     *
     * In this way, an RTL widget may operate normally inside an LTR page and vice versa.
     *
     * @member {Boolean} rtl
     */
    /**
     * This may be configured as `true` to make the widget's element use the `direction:rtl` style.
     *
     * All descendant widgets, including any {@link Core.widget.Widget#config-floating} widgets will
     * inherit this setting.
     * @config {Boolean}
     * @default false
     */
    rtl: null
  }), _a5;
};

// ../Core/lib/Core/widget/Widget.js
var assignValueDefaults = Object.freeze({
  highlight: false,
  onlyName: false
});
var EMPTY = Object.freeze([]);
var NO_SYNC = Object.freeze({ sync: false });
var floatRoots = [];
var hideModes = ["clip", "display", "opacity"];
var highlightExternalChange = "highlightExternalChange";
var isTransparent = /transparent|rgba\(0,\s*0,\s*0,\s*0\)/;
var renderConfigObserver = Symbol("renderConfigObserver");
var htmlRe3 = /<div|<button|<input|<p|<br|<li/;
var addElementListeners = (me, element, domConfig, refName) => {
  let listeners = (domConfig == null ? void 0 : domConfig.listeners) || (domConfig == null ? void 0 : domConfig.internalListeners);
  listeners = (listeners == null ? void 0 : listeners.on) || listeners;
  if (listeners) {
    const un = EventHelper.on(ObjectHelper.assign({
      element,
      thisObj: me
    }, listeners));
    if (refName) {
      (me._refListeners || (me._refListeners = /* @__PURE__ */ Object.create(null)))[refName] = un;
    } else {
      domConfig.listeners = {
        on: listeners,
        un
      };
    }
  }
};
var mergeAnim = (value2, was) => {
  return value2 && was && was[ObjectHelper.keys(value2)[0]] ? Config.merge(value2, was) : value2;
};
var widgetTriggerPaint = (w) => {
  w.isVisible && w.triggerPaint();
};
var negationPseudo = /^:not\((.+)\)$/;
var nonFlowedPositions = /absolute|fixed/i;
var isScaled = (w) => w.scale != null;
var { hasOwn: hasOwn6 } = ObjectHelper;
var { defineProperty: defineProperty6 } = Reflect;
var parseDuration = (d) => parseFloat(d) * (d.endsWith("ms") ? 1 : 1e3);
var alignedClass = [
  "b-aligned-above",
  "b-aligned-right",
  "b-aligned-below",
  "b-aligned-left"
];
var returnFalseProp = {
  configurable: true,
  value: false
};
var localizeRE = /(?:L\{([ \w.]+?)\})/;
var alignSpecRe2 = /^([trblc])(\d*)-([trblc])(\d*)$/i;
var mergeAlign = (oldValue, newValue) => {
  if (alignSpecRe2.test(oldValue)) {
    oldValue = { align: oldValue };
  }
  if (alignSpecRe2.test(newValue)) {
    newValue = { align: newValue };
  }
  return Config.merge(oldValue, newValue);
};
var callbackRe = /^[\w.]+$/;
var alignChangeDims = {
  1: "maxHeight",
  2: "height",
  4: "maxWidth",
  8: "width"
};
var isSingletonWidget = (w) => !w.isRipple && !w.closest((w2) => w2.isMessageDialog || w2.isSharedTooltip);
var getLayoutTarget = (el) => DomHelper.getStyleValue(el, "display") === "contents" ? getLayoutTarget(el.parentNode) : el;
var getEventTarget = (t) => {
  if (t == null ? void 0 : t.target) {
    const { ownerSVGElement } = t.target, svgRect = ownerSVGElement == null ? void 0 : ownerSVGElement.getBoundingClientRect(), target = getLayoutTarget(t.target), displayStyle = DomHelper.getStyleValue(t.target, "display");
    if (ownerSVGElement) {
      return {
        target: t.target.ownerSVGElement,
        offsetX: t.clientX - svgRect.x,
        offsetY: t.clientY - svgRect.y
      };
    }
    let { offsetX, offsetY } = t;
    if (displayStyle === "inline") {
      const targetRect = target.getBoundingClientRect();
      offsetX = t.clientX - targetRect.left;
      offsetY = t.clientY - targetRect.top;
    } else if (displayStyle === "contents") {
      const rect = target.getBoundingClientRect(target);
      offsetX -= rect.x;
      offsetY -= rect.y;
    }
    return {
      target: getLayoutTarget(t.target),
      offsetX,
      offsetY
    };
  }
};
var extractChildrenText = (result, item) => {
  result.push((item.text || item.html || "").trim());
  return result;
};
var _Widget = class _Widget extends Base.mixin(Localizable_default, Events_default, Delayable_default, Identifiable_default, Factoryable_default, KeyMap_default, RTL_default) {
  //region Config
  // Used when a config is a class and internally stored as a DomClassList
  // So that subclasses can add class names.
  static mergeCls(newValue, oldValue) {
    if (oldValue && newValue) {
      newValue = new DomClassList(oldValue).assign(typeof newValue === "string" ? new DomClassList(newValue) : newValue);
    } else if (newValue && !newValue.isDomClassList) {
      newValue = new DomClassList(newValue);
    }
    return newValue;
  }
  static get configurable() {
    return {
      /**
       * Get this widget's encapsulating HTMLElement, which is created along with the widget but added to DOM at
       * render time.
       * @member {HTMLElement} element
       * @readonly
       * @category DOM
       */
      /**
       * A {@link Core.helper.DomHelper#function-createElement-static} config object or HTML string from which to
       * create the Widget's element.
       * @private
       * @config {DomConfig|String}
       * @category DOM
       */
      element: true,
      /**
       * Set to `false` to not call onXXX method names (e.g. `onShow`, `onClick`), as an easy way to listen for events.
       *
       * ```javascript
       * const container = new Container({
       *     callOnFunctions : true
       *
       *     onHide() {
       *          // Do something when the 'hide' event is fired
       *     }
       * });
       * ```
       *
       * @config {Boolean}
       * @default
       */
      callOnFunctions: true,
      /**
       * Determines how a widget will prevent itself from being seen when {@link #property-hidden} is set to
       * `true` or the {@link #function-hide} method is called.
       *
       * The allowed values are as follows:
       *
       * - `'display'` (the default) to hide the widget using `display: none` style
       * - `'clip'` to  hide the widget using CSS `clip` style (only valid if `position: absolute`)
       * - `'opacity'` to hide the widget using `opacity : 0` style (and `pointer-events : none`)
       *
       * @prp {'clip'|'display'|'opacity'}
       * @internal
       * @default 'display'
       */
      hideMode: {
        $config: null,
        default: "display"
      },
      /**
       * Get/set widgets id
       * @member {String} id
       * @category DOM
       */
      /**
       * Widget id, if not specified one will be generated. Also used for lookups through Widget.getById
       * @config {String}
       * @category DOM
       */
      id: null,
      /**
       * The HTML to display initially or a function returning the markup (called at widget construction time).
       *
       * This may be specified as the name of a function which can be resolved in the component ownership
       * hierarchy, such as 'up.getHTML' which will be found on an ancestor Widget.
       *
       * @prp {String}
       * @param {Core.widget.Widget} widget The calling Widget
       * @returns {String}
       * @accepts {String|Function|DomConfig|DomConfig[]|VueConfig}
       * @category DOM
       */
      html: {
        $config: "lazy",
        value: null
      },
      /**
       * Set HTML content safely, without disturbing sibling elements which may have been
       * added to the {@link #property-contentElement} by plugins and features.
       * When specifying html, this widget's element will also have the {@link #config-htmlCls}
       * added to its classList, to allow targeted styling.
       * @member {String} content
       * @category DOM
       * @advanced
       */
      /**
       * The HTML content that coexists with sibling elements which may have been added to the
       * {@link #property-contentElement} by plugins and features.
       * When specifying html, this widget's element will also have the {@link #config-htmlCls}
       * class added to its classList, to allow targeted styling.
       * @config {String} content
       * @category DOM
       * @advanced
       */
      content: {
        $config: "lazy",
        value: null
      },
      /**
       * Custom CSS classes to add to element.
       * May be specified as a space separated string, or as an object in which property names
       * with truthy values are used as the class names:
       *
       * ```javascript
       *  cls : {
       *      'b-my-class'     : 1,
       *      [this.extraCls]  : 1,
       *      [this.activeCls] : this.isActive
       *  }
       * ```
       *
       * @prp {String|Object}
       * @category CSS
       */
      cls: {
        $config: {
          merge: "classList"
        },
        value: null
      },
      /**
       * Custom CSS class name suffixes to apply to the elements rendered by this widget. This may be specified
       * as a space separated string, an array of strings, or as an object in which property names with truthy
       * values are used as the class names.
       *
       * For example, consider a `Panel` with a `ui` config like so:
       *
       * ```javascript
       *  new Panel({
       *      text : 'OK',
       *      ui   : 'light'
       *  });
       * ```
       *
       * This will apply the CSS class `'b-panel-ui-light'` to the main element of the panel as well as its many
       * child elements. This allows simpler CSS selectors to match the child elements of this particular panel
       * UI:
       *
       * ```css
       *  .b-panel-content.b-panel-ui-light {
       *      background-color : #eee;
       *  }
       * ```
       *
       * Using the {@link #config-cls cls config} would make matching the content element more complex, and in
       * the presence of {@link Core.widget.Panel#config-strips docked items} and nested panels, impossible to
       * target accurately.
       *
       * @config {String|Object}
       * @category CSS
       */
      ui: {
        $config: {
          merge: "classList"
        },
        value: null
      },
      /**
       * Determines how a {@link Core.widget.Panel#config-collapsed} panel will treat this widget if it resides
       * within the panel's header (for example, as one of its {@link Core.widget.Panel#config-strips} or
       * {@link Core.widget.Panel#config-tools}).
       *
       * Valid options are:
       *  - `null` : The widget will be moved to the overlay header when the panel is collapsed (the default).
       *  - `false` : The widget will be unaffected when the panel is collapsed and will remain in the primary
       *    panel header at all times.
       *  - `'hide'` : The widget will be hidden when the panel is collapsed.
       *  - `'overlay'` : The widget will only appear in the collapsed panel's overlay header. See
       *    {@link Core.widget.panel.PanelCollapserOverlay collapsible type='overlay'}.
       *
       * @config {Boolean|'hide'|'overlay'}
       * @internal
       */
      collapsify: null,
      /**
       * Custom CSS classes to add to the {@link #property-contentElement}.
       * May be specified as a space separated string, or as an object in which property names
       * with truthy values are used as the class names:
       *
       * ```javascript
       *  cls : {
       *      'b-my-class'     : 1,
       *      [this.extraCls]  : 1,
       *      [this.activeCls] : this.isActive
       *  }
       * ```
       *
       * @config {String|Object}
       * @category CSS
       * @advanced
       */
      contentElementCls: {
        $config: {
          merge: "classList"
        },
        value: null
      },
      /**
       * Custom CSS classes to add to this widget's `element`. This property is typically used internally to
       * assign default CSS classes while allowing `cls` to alter these defaults. It is not recommended that
       * client code set this config but instead should set `cls`.
       *
       * For example, to remove a class defined by `defaultCls` using `cls`, declare the class name as a key with
       * a falsy value:
       *
       * ```javascript
       *  cls : {
       *      'default-class' : false
       *  }
       * ```
       *
       * @config {String|Object|String[]}
       * @internal
       */
      defaultCls: {
        $config: {
          merge: "classList"
        },
        value: null
      },
      /**
       * Controls the placement of this widget when it is added to a {@link Core.widget.Panel panel's }
       * {@link Core.widget.Panel#config-strips strips collection}. Typical values for this config are `'top'`,
       * `'bottom'`, `'left'`, or `'right'`, which cause the widget to be placed on that side of the panel's
       * body. Such widgets are called "edge strips".
       *
       * Also accepts direction neutral horizontal values `'start'` and `'end'`.
       *
       * If this config is set to `'header'`, the widget is placed in the panel's header, following the title. If
       * this config is set to `'pre-header'`, the widget is placed before the title. Such widgets are called
       * "header strips".
       *
       * @config {'top'|'bottom'|'left'|'right'|'start'|'end'|'header'|'pre-header'|Object} dock
       * @category Layout
       */
      dock: null,
      parent: null,
      /**
       * The {@link Core.widget.Tab tab} created for this widget when it is placed in a
       * {@link Core.widget.TabPanel}.
       * @member {Core.widget.Tab} tab
       * @readonly
       * @category Misc
       */
      /**
       * A configuration for the {@link Core.widget.Tab tab} created for this widget when it is placed in a
       * {@link Core.widget.TabPanel}. For example, this config can be used to control the icon of the `tab` for
       * this widget:
       *
       * ```javascript
       *  items : {
       *      panel : {
       *          type : 'panel',
       *          // other configs...
       *
       *          tab : {
       *              icon : 'b-fa-wrench'
       *          }
       *      },
       *      ...
       *  }
       * ```
       *
       * Another use for this config is to set the tab's {@link Core.widget.mixin.Rotatable#config-rotate} value
       * differently than the default managed by the `TabPanel`:
       *
       * ```javascript
       *  items : {
       *      panel : {
       *          type : 'panel',
       *          // other configs...
       *
       *          tab : {
       *              rotate : false   // don't rotate even if tabBar is docked left or right
       *          }
       *      },
       *      ...
       *  }
       * ```
       *
       * Set this to `false` to prevent the creation of a `tab` for this widget. In this case, this widget must
       * be {@link #function-show shown} explicitly. The {@link Core.widget.TabPanel#config-activeTab} for the
       * tab panel will be -1 in this situation.
       *
       * ```javascript
       *  items : {
       *      panel : {
       *          type : 'panel',
       *          tab  : false,    // no tab for this item
       *
       *          // other configs...
       *      },
       *      ...
       *  ]
       * ```
       *
       * @config {Boolean|TabConfig} tab
       * @category Misc
       */
      tab: null,
      /**
       * An object specifying attributes to assign to the root element of this widget.
       * Set `null` value to attribute to remove it.
       *
       * ```javascript
       * new Label({
       *     elementAttributes : {
       *        'data-role': 'primary', // Add data-role attribute with `"primary"` value
       *        inert : '',             // Add inert with no value
       *        title : null            // This will remove the title attribute from the element
       *     }
       * });
       * ```
       *
       * @config {Object<String,String|null>}
       * @category Misc
       */
      elementAttributes: null,
      /**
       * The CSS class(es) to add when HTML content is being applied to this widget.
       * @config {String|Object}
       * @category CSS
       */
      htmlCls: {
        $config: {
          merge: "classList"
        },
        value: {
          "b-html": 1
        }
      },
      /**
       * Custom style spec to add to element
       * @config {String}
       * @category CSS
       */
      style: null,
      /**
       * Get/set element's disabled state. Set to `'inert'` to also set the `inert` DOM attribute.
       * @member {Boolean|'inert'} disabled
       * @category Misc
       */
      /**
       * Disable or enable the widget. It is similar to {@link #config-readOnly} except a disabled widget
       * cannot be focused, uses a different rendition (usually greyish) and does not allow selecting its value.
       *
       * Set to `'inert'` to also set the `inert` DOM attribute.
       * @default false
       * @config {Boolean|'inert'}
       * @category Misc
       */
      disabled: null,
      /**
       * Get/set element's readOnly state. This is only valid if the widget is an input
       * field, __or contains input fields at any depth__. Updating this property will trigger
       * a {@link #event-readOnly} event.
       *
       * All descendant input fields follow the widget's setting. If a descendant
       * widget has a readOnly config, that is set.
       * @member {Boolean} readOnly
       * @category Misc
       */
      /**
       * Whether this widget is read-only.  This is only valid if the widget is an input
       * field, __or contains input fields at any depth__.
       *
       * All descendant input fields follow the widget's setting. If a descendant
       * widget has a readOnly config, that is set.
       * @default false
       * @config {Boolean}
       * @category Misc
       */
      readOnly: {
        value: null,
        default: false,
        $config: null
      },
      /**
       * Determines if the widgets read-only state should be controlled by its parent.
       *
       * When set to `true`, setting a parent container to read-only will not affect the widget. When set to
       * `false`, it will affect the widget.
       *
       * @category Misc
       * @config {Boolean}
       * @default false
       */
      ignoreParentReadOnly: null,
      /**
       * Element (or element id) to adopt as this Widget's encapsulating element. The widget's
       * content will be placed inside this element.
       *
       * If this widget has not been configured with an id, it will adopt the id of the element
       * in order to preserve CSS rules which may apply to the id.
       * @config {HTMLElement|String}
       * @default
       * @category DOM
       */
      adopt: null,
      /**
       * Element (or the id of an element) to append this widget's element to. Can be configured, or set once at
       * runtime. To access the element of a rendered widget, see {@link #property-element}.
       * @prp {HTMLElement}
       * @accepts {HTMLElement|String}
       * @category DOM
       */
      appendTo: null,
      /**
       * Element (or element id) to insert this widget before. If provided, {@link #config-appendTo} config is ignored.
       * @prp {HTMLElement|String}
       * @category DOM
       */
      insertBefore: null,
      /**
       * Element (or element id) to append this widget element to, as a first child. If provided, {@link #config-appendTo} config is ignored.
       * @prp {HTMLElement|String}
       * @category DOM
       */
      insertFirst: null,
      /**
       * Object to apply to elements dataset (each key will be used as a data-attribute on the element)
       * @config {Object<String,String>}
       * @category DOM
       */
      dataset: null,
      /**
       * Tooltip for the widget, either as a string or as a Tooltip config object.
       *
       * By default, the Widget will use a single, shared instance to display its tooltip as configured,
       * reconfiguring it to the specification before showing it. Therefore, it may not be permanently
       * mutated by doing things such as adding fixed event listeners.
       *
       * To have this Widget *own* its own `Tooltip` instance, add the property `newInstance : true`
       * to the configuration. In this case, the tooltip's {@link #property-owner} will be this Widget.
       *
       * __Note that in the absence of a configured {@link #config-ariaDescription}, the tooltip's value
       * will be used to populate an `aria-describedBy` element within this Widget.__
       *
       * @config {String|TooltipConfig|null}
       * @category Misc
       */
      tooltip: {
        $config: ["lazy", "nullify"],
        value: null
      },
      /**
       * Set to `false` to not show the tooltip when this widget is {@link #property-disabled}
       * @config {Boolean}
       * @default
       * @category Misc
       */
      showTooltipWhenDisabled: true,
      /**
       * Prevent tooltip from being displayed on touch devices. Useful for example for buttons that display a
       * menu on click etc, since the tooltip would be displayed at the same time.
       * @config {Boolean}
       * @default false
       * @category Misc
       */
      preventTooltipOnTouch: null,
      /**
       * When this is configured as `true` a [ResizeObserver](https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver)
       * is used to monitor this element for size changes caused by either style manipulation, or by CSS
       * layout.
       *
       * Size changes are announced using the {@link #event-resize} event.
       * @config {Boolean}
       * @default false
       * @category Misc
       * @advanced
       */
      monitorResize: {
        $config: ["lazy", "nullify"],
        value: null
      },
      /**
       * Set to `true` to apply the default mask to the widget. Alternatively, this can be the mask message or a
       * {@link Core.widget.Mask} config object.
       * @config {Boolean|String|MaskConfig}
       * @category Misc
       */
      masked: {
        $config: "nullify",
        value: null
      },
      /**
       * This config object contains the defaults for the {@link Core.widget.Mask} created for the
       * {@link #config-masked} config. Any properties specified in the `masked` config will override these
       * values.
       * @config {MaskConfig}
       * @category Misc
       */
      maskDefaults: {
        target: "element"
      },
      /**
       * Set to `true` to move the widget out of the document flow and position it
       * absolutely in browser viewport space.
       * @config {Boolean}
       * @default
       * @category Float & align
       */
      floating: null,
      /**
       * This config is used to absolutely position this widget in its parent {@link Core.widget.Container}. When
       * enabled, this widget will be rendered into the parent widget's primary {@link #property-element}. In
       * containers where the {@link #property-contentElement} is not the same as the `element` (for example,
       * {@link Core.widget.Panel}), the value of this config indicates if this widget should be rendered
       * `'before'` or `'after'` the `contentElement`.
       *
       * Because `positionable` widgets are `position: absolute` and may even be rendered outside of the owning
       * container's `contentElement`, these widgets do not participate in the standard layout of their container.
       *
       * It is not necessary to set this config explicitly when using the position configs {@link #config-top},
       *  {@link #config-right},  {@link #config-bottom}, or  {@link #config-left}, unless it needs to be set to
       *  `'after'`.
       *
       * @config {'before'|'after'|Boolean}
       * @default
       * @category Float & align
       * @internal
       */
      positionable: null,
      /**
       * Set to `true` when a widget is rendered into another widget's {@link #property-contentElement}, but must
       * not participate in the standard layout of that widget, and must be positioned relatively to that
       * widget's {@link #property-contentElement}.
       *
       * {@link Core.widget.Editor Editor}s are positioned widgets.
       * @config {Boolean}
       * @default
       * @category Float & align
       */
      positioned: null,
      /**
       * _Only valid if this Widget is {@link #config-floating}._
       * Set to `true` to be able to drag a widget freely on the page. Or set to an object with a ´handleSelector´
       * property which controls when a drag should start.
       *
       * ```javascript
       * draggable : {
       *     handleSelector : ':not(button)'
       * }
       * ```
       *
       * @config {Boolean|Object}
       * @property {String} handleSelector CSS selector used to determine if drag can be started from a
       * mouse-downed element inside the widget
       * @default false
       * @category Float & align
       */
      draggable: {
        $config: ["lazy", "nullify"],
        value: null
      },
      /**
       * _Only valid if this Widget is {@link #config-floating}._
       *
       * How to align this element with its target when {@link #function-showBy} is called
       * passing a simple element as an align target.
       *
       * Either a full alignment config object, or for simple cases, the edge alignment string to use.
       *
       * When using a simple string, the format is `'[trblc]n-[trblc]n'` and it specifies our edge and
       * the target edge plus optional offsets from 0 to 100 along the edges to align to. Also supports direction
       * independent edges horizontally, `s` for start and `e` for end (maps to `l` and `r` for LTR, `r` and `l`
       * for RTL).
       *
       * See the {@link #function-showBy} function for more details about using the object form.
       *
       * Once set, this is stored internally in object form.
       * @config {AlignSpec|String}
       * @category Float & align
       */
      align: {
        $config: {
          merge: mergeAlign
        },
        value: "t-b"
      },
      /**
       * _Only valid if this Widget is {@link #config-floating}._
       * Set to `true` to centre the Widget in browser viewport space.
       * @config {Boolean}
       * @default
       * @category Float & align
       */
      centered: null,
      /**
       * _Only valid if this Widget is {@link #config-floating} or {@link #config-positioned}._
       * Element, Widget or Rectangle to which this Widget is constrained.
       * @config {HTMLElement|Core.widget.Widget|Core.helper.util.Rectangle}
       * @default document.body
       * @category Float & align
       */
      constrainTo: void 0,
      /**
       * _Only valid if this Widget is {@link #config-floating} and being shown through {@link #function-showBy}._
       * `true` to show a connector arrow pointing to the align target.
       * @config {Boolean}
       * @default false
       * @category Float & align
       */
      anchor: null,
      /**
       * The owning Widget of this Widget. If this Widget is directly contained (that is, it is one of the
       * {@link Core.widget.Container#property-items} of a Container), this config will be ignored. In this case
       * the owner is __always__ the encapsulating Container.
       *
       * If this Widget is floating, this config should be specified by the developer.
       *
       * Registering with an `owner` creates a lifecycle relationship with that owning Widget. When the `owner` is
       * destroyed this widget will also be destroyed.
       *
       * This also allows focus to be tracked in the ownership tree. If a widget `owns` another widget, then even
       * if that other widget is in a different element, focusing that owned widget will *not* cause a
       * {@link #event-focusOut} event, and the {@link #property-containsFocus} property remains set.
       *
       * A widget can unregister from its `owner` and break this relationship at any time by setting the property
       * to `null`. It is then the developer's responsibility to ensure that this Widget is destroyed.
       *
       * @config {Core.widget.Widget}
       * @category Misc
       */
      owner: {
        $config: ["lazy", "nullify"],
        value: null
      },
      /**
       * Defines what to do if document is scrolled while Widget is visible (only relevant when floating is set to `true`).
       * Valid values: ´null´: do nothing, ´hide´: hide the widget or ´realign´: realign to the target if possible.
       * @config {'hide'|'realign'|null}
       * @default
       * @category Float & align
       */
      scrollAction: null,
      /**
       * _Only valid if this Widget is {@link #config-floating}._
       *
       * An object which defined which CSS style property should be animated upon hide, and how it should be
       * animated eg:
       *
       * ```javascript
       * {
       *    opacity: {
       *        to : 0,
       *        duration: '10s',
       *        delay: '0s'
       *    }
       * }
       * ```
       *
       * Set to `'false'` to disable animation.
       *
       * @config {Boolean|Object}
       * @category Float & align
       */
      hideAnimation: {
        $config: {
          merge: mergeAnim
        },
        value: null
      },
      /**
       * _Only valid if this Widget is {@link #config-floating}._
       *
       * An object which defined which CSS style property should be animated upon show, and how it should be
       * animated eg:
       *
       * ```javascript
       * {
       *    opacity: {
       *        to : 1,
       *        duration: '10s',
       *        delay: '0s'
       *    }
       * }
       * ```
       *
       * Set to `'false'` to disable animation.
       *
       * @config {Boolean|Object}
       * @category Float & align
       */
      showAnimation: {
        $config: {
          merge: mergeAnim
        },
        value: null
      },
      /**
       * The `top` CSS absolute position for this widget. If specified, the widget is implicitly configured as
       * {@link #config-positionable}.
       * @config {Number|String}
       * @internal
       */
      top: null,
      /**
       * The `inset-inline-end` CSS absolute position for this widget. If specified, the widget is implicitly
       * configured as {@link #config-positionable}.
       * @config {Number|String}
       * @internal
       */
      right: null,
      /**
       * The `bottom` CSS absolute position for this widget. If specified, the widget is implicitly configured as
       * {@link #config-positionable}.
       * @config {Number|String}
       * @internal
       */
      bottom: null,
      /**
       * The `inset-inline-start` CSS absolute position for this widget. If specified, the widget is implicitly
       * configured as {@link #config-positionable}.
       * @config {Number|String}
       * @internal
       */
      left: null,
      /**
       * The x position for the widget.
       *
       * _Only valid if this Widget is {@link #config-floating} and not aligned or anchored to an element._
       *
       * @config {Number}
       * @default
       * @category Float & align
       */
      x: null,
      /**
       * The y position for the widget.
       *
       * _Only valid if this Widget is {@link #config-floating} and not aligned or anchored to an element._
       *
       * @config {Number}
       * @default
       * @category Float & align
       */
      y: null,
      /**
       * Accessor to the {@link Core.helper.util.Scroller} which can be used
       * to both set and read scroll information.
       * @member {Core.helper.util.Scroller} scrollable
       * @category Layout
       */
      /**
       * Specifies whether (and optionally in which axes) a Widget may scroll. `true` means this widget may scroll
       * in both axes. May be an object containing boolean `overflowX` and `overflowY` properties which are
       * applied to CSS style properties `overflowX` and `overflowY`. If they are boolean, they are translated to
       * CSS overflow properties thus:
       *
       * - `true` -> `'auto'`
       * - `false` -> `'hidden'`
       *
       * After initialization, this property yields a {@link Core.helper.util.Scroller} which may be used to both
       * set and read scroll information.
       *
       * A Widget uses its {@link #property-overflowElement} property to select which element is to be scrollable. By default,
       * in the base `Widget` class, this is the Widget's encapsulating element. Subclasses may implement `get
       * overflowElement` to scroll inner elements.
       * @config {Boolean|ScrollerConfig|Core.helper.util.Scroller}
       * @default false
       * @category Scrolling
       */
      scrollable: {
        $config: ["lazy", "nullify"],
        value: null
      },
      /**
       * The class to instantiate to use as the {@link #config-scrollable}. Defaults to
       * {@link Core.helper.util.Scroller}.
       * @internal
       * @config {Core.helper.util.Scroller}
       * @typings {typeof Scroller}
       * @category Scrolling
       */
      scrollerClass: Scroller,
      /**
       * The name of the property to set when a single value is to be applied to this Widget. Such as when used
       * in a grid WidgetColumn, this is the property to which the column's `field` is applied.
       * @config {String}
       * @default 'html'
       * @category Misc
       */
      defaultBindProperty: "html",
      /**
       * Event that should be considered the default action of the widget. When that event is triggered the
       * widget is also expected to trigger an `action` event. Purpose is to allow reacting to most widgets in
       * a coherent way.
       * @private
       * @config {String}
       * @category Misc
       */
      defaultAction: null,
      /**
       * When set to `true`, this widget is considered as a whole when processing {@link Core.widget.Toolbar}
       * overflow. When `false`, this widget's child items are considered instead.
       *
       * When set to the string `'none'`, this widget is ignored by overflow processing. This option should be
       * used with caution as it prevents the overflow algorithm from moving such widgets into the overflow
       * popup which may result in not clearing enough space to avoid overflowing the toolbar.
       * @config {Boolean|String}
       * @default true
       * @category Layout
       * @internal
       */
      overflowable: {
        value: null,
        default: true,
        $config: null
      },
      /**
       * Widget's width, used to set element style.width. Either specify a valid width string or a number, which
       * will get 'px' appended. We recommend using CSS as the primary way to control width, but in some cases
       * this config is convenient.
       * @config {String|Number}
       * @category Layout
       */
      width: null,
      /**
       * Widget's height, used to set element style.height. Either specify a valid height string or a number, which
       * will get 'px' appended. We recommend using CSS as the primary way to control height, but in some cases
       * this config is convenient.
       * @config {String|Number}
       * @category Layout
       */
      height: null,
      /**
       * The element's maxHeight. Can be either a String or a Number (which will have 'px' appended). Note that
       * like {@link #config-height}, _reading_ the value will return the numeric value in pixels.
       * @config {String|Number}
       * @category Layout
       */
      maxHeight: null,
      /**
       * The elements maxWidth. Can be either a String or a Number (which will have 'px' appended). Note that
       * like {@link #config-width}, _reading_ the value will return the numeric value in pixels.
       * @config {String|Number}
       * @category Layout
       */
      maxWidth: null,
      /**
       * The elements minWidth. Can be either a String or a Number (which will have 'px' appended). Note that
       * like {@link #config-width}, _reading_ the value will return the numeric value in pixels.
       * @config {String|Number}
       * @category Layout
       */
      minWidth: null,
      /**
       * The element's minHeight. Can be either a String or a Number (which will have 'px' appended). Note that
       * like {@link #config-height}, _reading_ the value will return the numeric value in pixels.
       * @config {String|Number}
       * @category Layout
       */
      minHeight: null,
      // not public, only used by us in docs
      scaleToFitWidth: null,
      allowGrowWidth: true,
      // only used if scaleToFitWidth is true
      /**
       * Get element's margin property. This may be configured as a single number or a `TRBL` format string.
       * numeric-only values are interpreted as pixels.
       * @member {Number|String} margin
       * @category Layout
       */
      /**
       * Widget's margin. This may be configured as a single number or a `TRBL` format string.
       * numeric-only values are interpreted as pixels.
       * @config {Number|String}
       * @category Layout
       */
      margin: null,
      /**
       * Get element's flex property. This may be configured as a single number or a format string:
       *
       *      <flex-grow> <flex-shrink> <flex-basis>
       *
       * Numeric-only values are interpreted as the `flex-grow` value.
       * @member {Number|String} flex
       * @category Layout
       */
      /**
       * When this widget is a child of a {@link Core.widget.Container}, it will by default be participating in a
       * flexbox layout. This config allows you to set this widget's
       * <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/flex">flex</a> style.
       * This may be configured as a single number or a `<flex-grow> <flex-shrink> <flex-basis>` format string.
       * numeric-only values are interpreted as the `flex-grow` value.
       * @config {Number|String}
       * @category Layout
       */
      flex: null,
      /**
       * A widgets weight determines its position among siblings when added to a {@link Core.widget.Container}.
       * Higher weights go further down.
       * @config {Number}
       * @category Layout
       */
      weight: null,
      /**
       * Get/set this widget's `align-self` flexbox setting. This may be set to modify how this widget is aligned
       * within the cross axis of a flexbox layout container.
       * @member {String} alignSelf
       * @category Layout
       */
      /**
       * When this widget is a child of a {@link Core.widget.Container}, it will by default be participating in a
       * flexbox layout. This config allows you to set this widget's
       * <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/align-self">align-self</a> style.
       * @config {String}
       * @category Layout
       */
      alignSelf: null,
      /**
       * Configure as `true` to have the component display a translucent ripple when its
       * {@link #property-focusElement}, or {@link #property-element} is tapped *if the
       * current theme supports ripples*. Out of the box, only the Material theme supports ripples.
       *
       * This may also be a config object containing the properties listed below.
       *
       * E.g.
       *
       * ```javascript
       *    columns  : [{}...],
       *    ripple   : {
       *        color : 'red',
       *        clip  : '.b-grid-row'
       *    },
       *    ...
       * ```
       *
       * @config {Boolean|Object}
       * @property {String} [delegate] A CSS selector to filter which child elements trigger ripples. By default,
       * the ripple is clipped to the triggering element.
       * @property {String} [color='#000'] A CSS color name or specification.
       * @property {Number} [radius=100] The ending radius of the ripple. Note that it will be clipped by the
       * target element by default.
       * @property {String} [clip] A string which describes how to clip the ripple if it is not to be clipped to
       * the default element. Either the property of the widget to use as the clipping element, or a selector to
       * allow clipping to the closest matching ancestor to the target element.
       * @category Misc
       */
      ripple: null,
      /**
       * A title to display for the widget. Only in effect when inside a container that uses it (such as TabPanel)
       * @default
       * @config {String}
       * @category DOM
       */
      title: null,
      localizableProperties: ["title", "ariaLabel", "ariaDescription", "dataset.text"],
      // Set this flag to require element to have a size to be considered visible
      requireSize: false,
      /**
       * An identifier by which this widget will be registered in the {@link Core.widget.Container#property-widgetMap}
       * of all ancestor containers.
       *
       * If omitted, this widget will be registered using its {@link #config-id}. In most cases `ref` is
       * preferable over `id` since `id` is required to be globally unique while `ref` is not.
       *
       * The `ref` value is also added to the elements dataset, to allow targeting it using CSS etc.
       * @prp {String}
       * @readonly
       * @category Misc
       */
      ref: null,
      /**
       * Get/set the widget hidden state.
       *
       * Note: `hidden : false` does *not* mean that this widget is definitely visible.
       * To ascertain visibility, use the {@link #property-isVisible} property.
       * @member {Boolean} hidden
       * @category Visibility
       */
      /**
       * Configure with true to make widget initially hidden.
       * @default false
       * @config {Boolean}
       * @category Layout
       */
      hidden: null,
      /**
       * Text alignment: 'left', 'center' or 'right'. Also accepts direction neutral 'start' and 'end'.
       *
       * Applied by adding a `b-text-align-xx` class to the widgets element. Blank by default, which does not add
       * any alignment class.
       *
       * To be compliant with RTL, 'left' yields same result as 'start' and 'right' as 'end'.
       *
       * @config {'left'|'center'|'right'|'start'|'end'}
       * @category Layout
       */
      textAlign: null,
      // When adding our scroll listeners to hide/realign, we ignore events
      // happening too quickly as a result of the show/align action
      ignoreScrollDuration: 500,
      /**
       * The tag name of this Widget's root element
       * @config {String}
       * @default
       * @category DOM
       * @advanced
       */
      tag: "div",
      /**
       * Set this config to `false` to disable batching DOM updates on animation frames for this widget. This
       * has the effect of synchronously updating the DOM when configs affecting the rendered DOM are modified.
       * Depending on the situation, this could simplify code while increasing time spent updating the DOM.
       * @config {Boolean}
       * @default true
       * @internal
       */
      recomposeAsync: null,
      /**
       * If you are rendering this widget to a shadow root inside a web component, set this config to the shadowRoot. If not inside a web component, set it to `document.body`
       * @config {ShadowRoot|HTMLElement}
       * @default
       * @category Misc
       */
      rootElement: null,
      htmlMutationObserver: {
        $config: ["lazy", "nullify"],
        value: {
          childList: true,
          subtree: true
        }
      },
      role: {
        $config: {
          lazy: true,
          default: null
        },
        value: "presentation"
      },
      /**
       * A localizable string (May contain `'L{}'` tokens which resolve in the locale file) to inject as
       * the `aria-label` attribute.
       *
       * This widget is passed as the `templateData` so that functions in the locale file can
       * interrogate the widget's state.
       * @config {String}
       * @category Accessibility
       * @advanced
       */
      ariaLabel: {
        $config: "lazy",
        value: null
      },
      /**
       * A localizable string (May contain `'L{}'` tokens which resolve in the locale file) to inject
       * into an element which will be linked using the `aria-describedby` attribute.
       *
       * This widget is passed as the `templateData` so that functions in the locale file can
       * interrogate the widget's state.
       * @config {String}
       * @category Accessibility
       * @advanced
       */
      ariaDescription: {
        $config: "lazy",
        value: null
      },
      /**
       * The `aria-live` value for this widget.
       *
       * See https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Live_Regions
       * @prp {String}
       * @deprecated Will be removed in 7.0
       * @category Accessibility
       */
      ariaLive: {
        $config: "lazy",
        value: null
      },
      ariaElement: "element",
      ariaHasPopup: null,
      realignTimeout: 300,
      testConfig: {
        ignoreScrollDuration: 100,
        realignTimeout: 50
      },
      /**
       * _Only valid if this Widget is {@link #config-floating}._
       *
       * When configured as `true`, this widget uses {@link Core.helper.BrowserHelper#property-isMobile-static}
       * to maximize itself on mobile devices.
       * @prp {Number|String}
       * @category Float & align
       */
      maximizeOnMobile: null,
      /**
       * The events to forward from an overflow twin to its origin widget.
       *
       * May be specified as a space separated string, or as an object in which property names
       * with truthy values are used as the event names:
       *
       * ```javascript
       *  twinForwardEvents : {
       *      change : true,
       *      input  : 1
       *  }
       * ```
       *
       * NOTE: This config cannot be dynamically changed after the `overflowTwin` has been created (see
       * {@link #function-ensureOverflowTwin}.
       * @config {String|String[]|Object}
       * @internal
       */
      twinForwardEvents: {
        $config: {
          merge: "classList"
        },
        value: null
      },
      /**
       * A mapping object describing config properties to sync with the `overflowTwin`. This is pass to
       * {@link Core.Base#function-bindConfigs}.
       *
       * For example:
       *
       * ```javascript
       *  twinSyncConfigs : {
       *      disabled : true,
       *      value    : 'checked'   // sync twin's checked config to this object's value config
       *  }
       * ```
       *
       * NOTE: This config cannot be dynamically changed after the `overflowTwin` has been created (see
       * {@link #function-ensureOverflowTwin}.
       * @config {String|String[]|Object}
       * @internal
       */
      twinSyncConfigs: {
        $config: {
          merge: "classList"
        },
        value: ["disabled"]
      }
    };
  }
  static get prototypeProperties() {
    return {
      /**
       * true if no id was set, will use generated id instead (widget1, ...). Toggle automatically on creation
       * @member {Boolean} hasGeneratedId
       * @private
       * @category Misc
       */
      hasGeneratedId: false,
      /**
       * This property is set to `true` after processing the initial paint for the widget. It remains `false`
       * during the initial paint. The intended use for this flag is to avoid processing that will be handled by
       * the first paint (similar to not firing events during the widget's initial configuration). If this field
       * is `true`, the initial paint has already taken place, otherwise, it has yet to run. This field differs
       * from `isPainted` which checks that the widget's element is attached to the DOM.
       * @member {Boolean} hasPainted
       * @internal
       * @readonly
       * @category DOM
       */
      hasPainted: false,
      /**
       * This readonly property is `true` for normal widgets in the {@link Core.widget.Container#config-items} of
       * a container. It is `false` for special widgets such as a {@link Core.widget.Panel#config-tbar}.
       * @member {Boolean} innerItem
       * @internal
       * @category Misc
       */
      innerItem: true
    };
  }
  static get declarable() {
    return [
      /**
       * This property declares the set of config properties that affect a Widget's rendering, i.e., the configs
       * used by the {@link #function-compose} method.
       *
       * For example:
       *
       * ```javascript
       *  class Button extends Widget {
       *      static renderConfigs = [ 'cls', 'iconCls', 'text' ];
       *  }
       * ```
       *
       * Alternatively this can be an object:
       *
       * ```javascript
       *  class Button extends Widget {
       *      static renderConfigs = {
       *          cls     : true,
       *          iconCls : true,
       *          text    : true
       *      };
       *  }
       * ```
       *
       * @member {Object|String[]} renderConfigs
       * @static
       * @category Configuration
       * @internal
       */
      "renderConfigs"
    ];
  }
  /**
   * An object providing the `record` and `column` for a widget embedded inside a {@link Grid.column.WidgetColumn}
   *
   * ```javascript
   * columns : [
   *    {
   *        type   : 'widget',
   *        widgets: [{
   *            type     : 'button',
   *            icon     : 'b-fa b-fa-trash',
   *            onAction : ({ source : btn }) => btn.cellInfo.record.remove()
   *        }]
   *    }
   * ]
   * ```
   *
   * @readonly
   * @member {CellWidgetContext} cellInfo
   * @category Misc
   */
  static get delayable() {
    return {
      recompose: "raf",
      doHideOrRealign: "raf",
      // Screen size and orientation changes must be buffered in line with.
      // ResponsiveMixin whose responsiveUpdate method is on a RAF.
      onAlignConstraintChange: "raf"
    };
  }
  static get factoryable() {
    return {
      defaultType: "widget"
    };
  }
  static get identifiable() {
    return {};
  }
  /**
   * Returns an array containing all existing Widgets. The returned array is generated by this call and is not an
   * internal structure.
   * @property {Core.widget.Widget[]}
   * @readonly
   * @internal
   */
  static get all() {
    return super.all;
  }
  /**
   * Get/set the {@link #config-recomposeAsync} config for all widgets. Setting this value will set the config for
   * all existing widgets and will be the default value for newly created widgets. Set this value to `null` to disable
   * the default setting for new widgets while leaving existing widgets unaffected.
   * @property {Boolean}
   * @internal
   */
  static get recomposeAsync() {
    return _Widget._recomposeAsync;
  }
  static set recomposeAsync(value2) {
    _Widget._recomposeAsync = value2;
    if (value2 != null) {
      const { all } = _Widget;
      for (let i = 0; i < all.length; ++i) {
        if (all[i].isComposable) {
          all[i].recomposeAsync = value2;
        }
      }
    }
  }
  updateWeight() {
    var _a5, _b2;
    if (this.isPainted) {
      (_b2 = (_a5 = this.parent) == null ? void 0 : _a5.onChildWeightChange) == null ? void 0 : _b2.call(_a5, this);
    }
  }
  isA(type) {
    return this.isType(type, true);
  }
  isType(type, deep) {
    return _Widget.isType(this, type, deep);
  }
  static setupRenderConfigs(cls, meta) {
    const classRenderConfigs = meta.getInherited("renderConfigs"), { renderConfigs } = cls;
    if (renderConfigs) {
      if (Array.isArray(renderConfigs)) {
        for (const name of renderConfigs) {
          classRenderConfigs[name] = true;
        }
      } else {
        ObjectHelper.assign(classRenderConfigs, renderConfigs);
      }
      classRenderConfigs[renderConfigObserver] = null;
    }
  }
  // Allowing string weights (used by subgrids for backwards compatibility)
  static weightSortFn({ weight: a }, { weight: b }) {
    if (typeof a === "string" || typeof b === "string") {
      return String(a).localeCompare(String(b));
    }
    return (a != null ? a : Number.MAX_SAFE_INTEGER) - (b != null ? b : Number.MAX_SAFE_INTEGER);
  }
  /**
   * Call once per class for custom widgets to have them register with the `Widget` class, allowing them to be created
   * by type.
   *
   * For example:
   *
   * ```javascript
   * class MyWidget extends Widget {
   *   static get type() {
   *     return 'mywidget';
   *   }
   * }
   * MyWidget.initClass();
   * ```
   *
   * @method initClass
   * @static
   * @category Lifecycle
   * @advanced
   */
  //endregion
  //region Init & destroy
  construct(config = {}, ...args) {
    const me = this, { domSyncCallback } = me, { recomposeAsync } = _Widget;
    if (recomposeAsync != null && me.recomposeAsync == null) {
      me.recomposeAsync = recomposeAsync;
    }
    if (!globalThis.bryntum.cssVersion) {
      const cssVersion = globalThis.bryntum.cssVersion = CSSHelper.getCSSVersion(), jsVersion = VersionHelper.getVersion("core");
      if (cssVersion && cssVersion !== jsVersion) {
        console.warn(`CSS version ${cssVersion} doesn't match bundle version ${jsVersion}!
Make sure you have imported css from the appropriate product version.`);
      }
    }
    me.configureAriaDescription = config.ariaDescription;
    me._isAnimatingCounter = 0;
    me.alignmentChanges = 0;
    me.byRef = /* @__PURE__ */ Object.create(null);
    me.onTargetResize = me.onTargetResize.bind(me);
    me.onFullscreenChange = me.onFullscreenChange.bind(me);
    me.domSyncCallback = domSyncCallback.$nullFn ? null : domSyncCallback.bind(me);
    me._isUserAction = false;
    super.construct(config, ...args);
    me.finalizeInit();
  }
  startConfigure(config) {
    super.startConfigure(config);
    this.getConfig("element");
  }
  /**
   * Creates a new widget instance based on the passed config object. The config object is require to have a `type` property which determines what kind of widget to create.
   * @function create
   * @static
   * @param {ContainerItemConfig} config Config object with `type` string property
   * @typings config -> {ContainerItemConfig|Object}
   * @returns {Object} The instance of the created Widget
   * @category Lifecycle
   */
  /**
   * Called by the Base constructor after all configs have been applied.
   * @internal
   * @category Lifecycle
   */
  finalizeInit() {
    var _a5;
    const me = this, { adopt } = me, refElement = me.insertBefore || me.appendTo || me.insertFirst || adopt, { classList } = me.element;
    me.getConfig("html", "content");
    if (refElement) {
      if (adopt) {
        classList.remove("b-hidden");
        me._hidden = false;
      }
      if (me.owner || ((_a5 = refElement.nodeType ? refElement : document.getElementById(refElement)) == null ? void 0 : _a5.isConnected)) {
        me.render();
      } else {
        me.onFirstResizeAfterConnect = me.onFirstResizeAfterConnect.bind(me);
        ResizeMonitor.addResizeListener(refElement, me.onFirstResizeAfterConnect);
      }
    }
    classList.remove("b-initializing");
  }
  onFirstResizeAfterConnect(el) {
    ResizeMonitor.removeResizeListener(el, this.onFirstResizeAfterConnect);
    if (!this.isDestroyed && !this.rendered) {
      this.render();
    }
  }
  doDestroy() {
    var _a5, _b2, _c2;
    const me = this, {
      preExistingElements,
      element,
      adopt,
      _refListeners,
      _rootElement,
      eventRoot,
      ownedWidgets,
      parent
    } = me;
    me.updateOwner(null, me.owner);
    if (Fullscreen.element === element) {
      Fullscreen.exit();
    }
    if (_refListeners) {
      Object.values(_refListeners, (un) => un());
      me._refListeners = null;
    }
    if (element) {
      const sharedTooltip = !me._tooltip && _rootElement && ((_a5 = _Widget.Tooltip) == null ? void 0 : _a5.getSharedTooltip(_rootElement, eventRoot, true));
      if ((sharedTooltip == null ? void 0 : sharedTooltip.owner) === me) {
        sharedTooltip.owner = null;
        sharedTooltip.hide();
      }
      me.onExitFullscreen();
      me.clearTimeout("scrollListenerTimeout");
      me.clearTimeout("resizeListenerTimeout");
      me.removeTransientListeners();
      if (me.floating || me.positioned || me.positionable) {
        me.isPainted && me.hide(false);
      } else {
        me.revertFocus();
      }
      ResizeMonitor.removeResizeListener(element.parentElement, me.onParentElementResize);
      ResizeMonitor.removeResizeListener(element, me.onElementResize);
      if (adopt) {
        for (let nodes = Array.from(element.childNodes), i = 0, { length } = nodes; i < length; i++) {
          const el = nodes[i];
          if (!preExistingElements.includes(el) && el !== me.floatRoot) {
            el.remove();
          }
        }
        element.className = me.adoptedElementClassName;
        element.style.cssText = me.adoptedElementCssText;
      }
    }
    (_b2 = me.connectedObserver) == null ? void 0 : _b2.disconnect();
    (parent == null ? void 0 : parent.isContainer) && !parent.isDestroying && ((_c2 = parent._items) == null ? void 0 : _c2.includes(me)) && parent.remove(me);
    super.doDestroy();
    ownedWidgets && Object.values(ownedWidgets).forEach((w) => {
      var _a6;
      return (_a6 = w.destroy) == null ? void 0 : _a6.call(w);
    });
    eventRoot && Promise.resolve().then(() => {
      var _a6, _b3;
      if (!Object.values(_Widget.identifiable.idMap).some(isSingletonWidget)) {
        (_b3 = (_a6 = _Widget.Ripple) == null ? void 0 : _a6.cleanUpRippleListeners) == null ? void 0 : _b3.call(_a6);
        GlobalEvents_default.detachFocusListeners(eventRoot);
      }
    });
    if (!adopt) {
      element.remove();
    }
  }
  //endregion
  //region Values
  get assignValueDefaults() {
    return assignValueDefaults;
  }
  get valueName() {
    return this.name || this.ref || this.id;
  }
  getValueName(onlyName) {
    onlyName = onlyName && typeof onlyName === "object" ? onlyName.onlyName : onlyName;
    return onlyName ? this.name : this.valueName;
  }
  assignFieldValue(values, key, value2) {
    const me = this, valueBindProperty = me.defaultBindProperty;
    if (valueBindProperty in me) {
      me[valueBindProperty] = value2;
    }
  }
  assignValue(values, options = assignValueDefaults) {
    const me = this, hec = me[highlightExternalChange], key = me.getValueName(options);
    if (key && (!values || key in values)) {
      if (options.highlight === false) {
        me[highlightExternalChange] = false;
      }
      me.assignFieldValue(values, key, values ? values[key] : null);
      me[highlightExternalChange] = hec;
    }
  }
  gatherValue(values) {
    const me = this, valueBindProperty = me.defaultBindProperty;
    if (me.constructor !== _Widget && valueBindProperty in me) {
      values[me.name || me.ref || me.id] = me[valueBindProperty];
    }
  }
  gatherValues(values) {
    this.eachWidget((widget) => widget.gatherValue(values), false);
  }
  //endregion
  addToOverflowMenu(menu, overrides, onCreateTwin2) {
    const overflowTwin = this.ensureOverflowTwin(overrides, onCreateTwin2);
    menu.items.push(overflowTwin);
    return overflowTwin;
  }
  get twinForwardEventPairs() {
    const value2 = this._twinForwardEvents;
    return value2 ? ObjectHelper.entries(value2) : EMPTY;
  }
  /**
   * This widget's twin that is placed in an overflow menu when this widget has been hidden by its owner, typically
   * a {@link Core.widget.Toolbar} due to {@link Core.widget.Toolbar#config-overflow}. The `overflowTwin` is created
   * lazily by {@link #function-ensureOverflowTwin}.
   *
   * @member {Core.widget.Widget} overflowTwin
   * @readonly
   * @internal
   */
  /**
   * This method returns the config object to use for creating this widget's {@link #property-overflowTwin}.
   *
   * @param {Function|Object} [overrides] If an object is passed, it is a set of config properties to override the
   * config object returned by {@link #function-configureOverflowTwin}. If a function is passed, it is called with
   * the config object. The function may either alter the object it is given or return a replacement.
   * @returns {Object} The `overflowTwin` config object
   * @internal
   */
  configureOverflowTwin(overrides) {
    const me = this, { twinSyncConfigs } = me;
    let config = ObjectHelper.clone(me.initialConfig), key, val;
    delete config.id;
    delete config.listeners;
    delete config.internalListeners;
    delete config.parent;
    config.hidden = false;
    config.type = me.type;
    config._overflowTwinOrigin = me;
    config.owner = me.owner;
    me.twinForwardEventPairs.forEach(([ev]) => {
      delete config[`on${StringHelper.capitalize(ev)}`];
    });
    if (twinSyncConfigs) {
      for (key in twinSyncConfigs) {
        val = twinSyncConfigs[key];
        if (val) {
          delete config[key];
          config[typeof val === "string" ? val : key] = me[key];
        }
      }
    }
    if (overrides) {
      config = typeof overrides === "function" ? overrides(config) || config : ObjectHelper.assign(config, overrides);
    }
    return config;
  }
  /**
   * This method creates the {@link #property-overflowTwin} for this widget. It is called by
   * {@link #function-ensureOverflowTwin} if the `overflowTwin` does not yet exist.
   *
   * The config for the {@link #property-overflowTwin} is produced by {@link #function-configureOverflowTwin}.
   *
   * @param {Function|Object} [overrides] If an object is passed, it is a set of config properties to override the
   * config object returned by {@link #function-configureOverflowTwin}. If a function is passed, it is called with
   * the config object. The function may either alter the object it is given or return a replacement.
   * @returns {Core.widget.Widget} The `overflowTwin`
   * @internal
   */
  createOverflowTwin(overrides) {
    const me = this, config = me.configureOverflowTwin(overrides), overflowTwin = _Widget.create(config), { twinSyncConfigs } = me;
    twinSyncConfigs && me.bindConfigs(overflowTwin, twinSyncConfigs, NO_SYNC);
    me.twinForwardEventPairs.forEach(([evFrom, evTo]) => {
      overflowTwin.ion({
        [evFrom]: (info) => {
          if (me.overflowTwin === info.source) {
            info = ObjectHelper.assign({}, info);
            delete info.source;
            me.trigger(typeof evTo === "string" ? evTo : info.eventName, info);
          }
        }
      });
    });
    return overflowTwin;
  }
  /**
   * This method returns the existing {@link #property-overflowTwin} or creates it, if it has not yet been created
   * (see {@link #function-createOverflowTwin}).
   *
   * @param {Function|Object} [overrides] If an object is passed, it is a set of config properties to override the
   * config object returned by {@link #function-configureOverflowTwin}. If a function is passed, it is called with
   * the config object. The function may either alter the object it is given or return a replacement.
   * @param {Function} [onCreate] A function to call when the `overflowTwin` is initially created.
   * @returns {Core.widget.Widget} The `overflowTwin`
   * @internal
   */
  ensureOverflowTwin(overrides, onCreate) {
    let { overflowTwin } = this;
    if (!overflowTwin) {
      this.overflowTwin = overflowTwin = this.createOverflowTwin(overrides);
      onCreate == null ? void 0 : onCreate(overflowTwin);
    }
    return overflowTwin;
  }
  //---------------------------------------------------------------------------------------------------------
  //region Render
  /**
   * Returns `true` if this class uses `compose()` to render itself.
   * @returns {Boolean}
   * @internal
   */
  get isComposable() {
    return !this.compose.$nullFn;
  }
  adoptElement(adopt) {
    const me = this, adoptElement = typeof adopt === "string" ? document.getElementById(adopt) : adopt, previousHolder = _Widget.fromElement(adoptElement);
    if (previousHolder && previousHolder.adopt && previousHolder !== me) {
      const previousHolderAdopt = typeof previousHolder.adopt === "string" ? document.getElementById(previousHolder.adopt) : previousHolder.adopt;
      if (previousHolderAdopt === adoptElement) {
        previousHolder.destroy();
      }
    }
    me.preExistingElements = Array.from(adoptElement.childNodes);
    me.adoptedElementClassName = adoptElement.className;
    me.adoptedElementCssText = adoptElement.style.cssText;
    adoptElement.lastDomConfig = null;
    adoptElement.$refOwnerId = me.id;
    adoptElement.className = adoptElement.style.cssText = "";
    return adoptElement;
  }
  /**
   * Defines an element reference accessor on the class prototype. This accessor is used to flush any pending DOM
   * changes prior to accessing such elements.
   * @param {String} name
   * @param {String} key
   * @private
   */
  addRefAccessor(name, key) {
    const { prototype: prototype2 } = this.constructor;
    defineProperty6(prototype2, key, {
      writable: true,
      value: null
    });
    defineProperty6(prototype2, name, {
      get() {
        this.recompose.flush();
        return this[key];
      },
      set(el) {
        this[key] = el;
      }
    });
  }
  /**
   * This method is called by `DomHelper.createElement` and `DomSync.sync` as new reference elements are created.
   * @param {String} name The name of the element, i.e., the value of its `reference` attribute.
   * @param {HTMLElement} el The element instance
   * @param {DomConfig} [domConfig] The DOM config object.
   * @internal
   */
  attachRef(name, el, domConfig) {
    const me = this, key = "_" + name;
    el.dataset && (el.dataset.ownerCmp = me.id);
    if (me.isComposable) {
      if (!(key in me)) {
        me.addRefAccessor(name, key);
      }
      addElementListeners(me, el, domConfig, name);
    }
    me.byRef[name] = el;
    me[name] = el;
  }
  /**
   * This method is called by `DomSync.sync` as reference elements are removed from the DOM.
   * @param {String} name The name of the element, i.e., the value of its `reference` attribute.
   * @param {HTMLElement} el The element instance
   * @param {DomConfig} domConfig The DOM config object.
   * @internal
   */
  detachRef(name, el, domConfig) {
    const me = this, listeners = me._refListeners;
    if (listeners == null ? void 0 : listeners[name]) {
      listeners[name]();
      delete listeners[name];
    }
    me[name] = null;
    delete me.byRef[name];
  }
  /**
   * This method is called following an update to the widget's rendered DOM.
   * @internal
   */
  afterRecompose() {
  }
  /**
   * Returns a {@link Core.helper.DomHelper#function-createElement-static} config object that defines this widget's
   * DOM structure. This object should be determined using {@link Core.Base#property-configurable-static} properties
   * to ensure this method is called again if these properties are modified.
   *
   * For more information see {@link Core.widget.Widget class documentation}.
   * @returns {DomConfig}
   * @advanced
   */
  compose() {
    const { widgetClassList } = this;
    return {
      class: {
        ...DomClassList.normalize(widgetClassList, "object")
      }
    };
  }
  /**
   * This method iterates the class hierarchy from Widget down to the class of this instance and calls any `compose`
   * methods implemented by derived classes.
   * @returns {Object}
   * @private
   */
  doCompose() {
    const me = this, { $meta: meta } = me, classes = meta.hierarchy, renderConfigs = meta.renderConfigs || meta.getInherited("renderConfigs");
    let { composers } = meta, domConfig = null, c, key, i, proto4;
    me.recompose.suspend();
    if (!composers) {
      meta.composers = composers = [];
      for (i = classes.indexOf(_Widget); i < classes.length; ++i) {
        proto4 = classes[i].prototype;
        if (hasOwn6(proto4, "compose")) {
          composers.push(proto4);
        }
      }
      if (!hasOwn6(renderConfigs, renderConfigObserver)) {
        renderConfigs[renderConfigObserver] = {
          get(name) {
            if (name !== "element") {
              renderConfigs[name] = true;
            }
          }
        };
      }
    }
    me.configObserver = renderConfigs[renderConfigObserver];
    for (i = 0; i < composers.length; ++i) {
      c = composers[i].compose.call(me, domConfig);
      domConfig = domConfig ? DomHelper.merge(domConfig, c) : c;
    }
    if (hasOwn6(me, "compose")) {
      DomHelper.merge(domConfig, me.compose(domConfig));
    }
    me.configObserver = null;
    return DomHelper.normalizeChildren(domConfig, (childName, hoist) => {
      if (hoist) {
        key = "_" + childName;
        if (!(key in me)) {
          me.addRefAccessor(childName, key);
        }
      }
    });
  }
  get element() {
    if (this.isComposable && !this.isDestroying) {
      this.recompose.flush();
    }
    return this._element;
  }
  /**
   * Template method called during DOM updates. See {@link Core.helper.DomSync#function-sync-static DomSync.sync()}.
   * @param {Object} info Properties describing the sync action taken.
   * @internal
   */
  domSyncCallback(info) {
  }
  changeElement(element) {
    const me = this, compose = me.isComposable, { adopt } = me;
    me.id = me.id || (adopt == null ? void 0 : adopt.id) || null;
    if (adopt) {
      element = me.adoptElement(adopt);
    } else {
      if (compose) {
        element = me.doCompose();
      }
    }
    if (typeof element === "string") {
      element = DomHelper.createElementFromTemplate(element);
    } else if (ObjectHelper.isObject(element)) {
      element = DomHelper.createElement(element, {
        refOwner: me,
        callback: me.domSyncCallback
        // mimic DomSync callbacks (needed by TaskBoard)
      });
      me.recompose.resume();
    } else if (DomHelper.isReactElement(me.peekConfig("html")) || DomHelper.isVueConfig(me.peekConfig("html"))) {
      element = document.createElement("div");
    } else if (element.nodeType !== 1) {
      element = DomHelper.createElementFromTemplate(me.template(me));
    }
    !element.id && (element.id = me.id);
    if (me.elementAttributes) {
      DomHelper.setAttributes(element, me.elementAttributes);
    }
    return element;
  }
  updateElement(element) {
    const me = this, compose = me.isComposable;
    if (me.adopt) {
      me._hidden = true;
      compose && me.recomposeNow();
    }
    compose && addElementListeners(me, element, element.lastDomConfig);
    const { className } = element, {
      contentElement,
      contentElementCls,
      isComposable
    } = me, hasChildContent = contentElement !== element, namedElements = !isComposable && element.querySelectorAll("[data-reference]"), classes = isComposable ? [] : me.widgetClassList;
    let uiClasses = (hasChildContent || !isComposable) && me.uiClasses;
    className && classes.unshift(className);
    me._hidden && classes.push("b-hidden", `b-hide-mode-${me.hideMode}`);
    me._readOnly && classes.push("b-readonly");
    classes.push("b-initializing");
    FunctionHelper.noThrow(
      element,
      "remove"
      /*, () => { debugger; } /**/
    );
    if (uiClasses) {
      if (contentElementCls == null ? void 0 : contentElementCls.value) {
        uiClasses = uiClasses.slice();
        uiClasses.push(contentElementCls.value);
      }
      uiClasses = uiClasses.join(" ");
    } else {
      uiClasses = contentElementCls == null ? void 0 : contentElementCls.value;
    }
    if (uiClasses) {
      if (hasChildContent) {
        contentElement.className += " " + uiClasses;
      } else {
        classes.push(uiClasses);
      }
    }
    if (!me.parent) {
      const { defaultCls } = me, { outerCls } = _Widget;
      classes.push(...defaultCls ? outerCls.filter((c) => !(c in defaultCls) || defaultCls[c]) : outerCls);
    }
    element.className = classes.join(" ");
    if (namedElements) {
      for (let el, i = 0; i < namedElements.length; ++i) {
        el = namedElements[i];
        me.attachRef(el.getAttribute("data-reference"), el);
        el.removeAttribute("data-reference");
      }
    }
    if (!me.scaleToFitWidth) {
      me.getConfig("monitorResize");
    }
    me.getConfig("role", "ariaLabel", "ariaDescription");
    if (me._html) {
      me.getConfig("htmlMutationObserver");
    }
    if (me.owner && !me._owner) {
      me.updateOwner(me.owner);
    }
    if (DomHelper.isVueConfig(me._html)) {
      DomHelper.handleVueContent(me, me.html);
    }
    me.trigger("elementCreated", { element });
  }
  localizeText(text) {
    return text.match(localizeRE) ? this.L(text, this) : text;
  }
  updateAriaDescription(ariaDescription) {
    const me = this, { ariaElement } = me, descElId = `${me.id}-aria-desc-el`;
    if (ariaDescription) {
      const ariaDescEl = me._ariaDescEl || (me._ariaDescEl = DomHelper.createElement({
        className: "b-aria-desc-element",
        id: descElId,
        parent: ariaElement
      }));
      ariaDescEl.innerText = me.localizeText(ariaDescription);
      DomHelper.addAttributeValue(ariaElement, "aria-describedBy", descElId);
    } else {
      DomHelper.removeAttributeValue(ariaElement, "aria-describedBy", descElId);
    }
  }
  updateAriaLabel(ariaLabel = "") {
    const el = this.labelledElement || this.ariaElement;
    el && DomHelper.setAttributes(el, {
      "aria-label": ariaLabel ? this.localizeText(ariaLabel) : null
    });
  }
  updateAriaHasPopup(ariaHasPopup) {
    var _a5, _b2;
    if (ariaHasPopup) {
      (_a5 = this.ariaElement) == null ? void 0 : _a5.setAttribute("aria-haspopup", ariaHasPopup);
    } else {
      (_b2 = this.ariaElement) == null ? void 0 : _b2.removeAttribute("aria-haspopup");
    }
  }
  updateAriaLive(ariaLive) {
    DomHelper.setAttributes(this.ariaElement, {
      "aria-live": ariaLive
    });
  }
  updateRole(role) {
    var _a5, _b2;
    if (role) {
      (_a5 = this.ariaElement) == null ? void 0 : _a5.setAttribute("role", role);
    } else {
      (_b2 = this.ariaElement) == null ? void 0 : _b2.removeAttribute("role");
    }
  }
  // For when an owner needs a role other than the default
  get ownersRole() {
    var _a5;
    let { role } = this;
    if (role === "presentation") {
      role = (_a5 = this.query((e) => e.role !== "presentation")) == null ? void 0 : _a5.role;
    }
    return role;
  }
  get ariaElement() {
    this.getConfig("element");
    const { _ariaElement } = this;
    return _ariaElement.nodeType === Node.ELEMENT_NODE ? _ariaElement : ObjectHelper.getPath(this, _ariaElement);
  }
  /**
   * This method determines if this widget (typically a {@link Core.widget.Tool}) should be placed in the header of
   * the calling {@link Core.widget.Panel}.
   * @param {Object} options An object specifying various options.
   * @param {Boolean} options.collapsed True if the panel is in a {@link Core.widget.Panel#config-collapsed} state.
   * @param {Boolean} options.alt True if the panel is rendering its alternate panel header, false for the primary header.
   * @returns {Boolean}
   * @internal
   */
  isCollapsified({ collapsed, alt: alt2 }) {
    const { collapsify } = this, hideIfCollapsed = collapsify === "hide", alwaysPrimary = collapsify === false, altIfCollapsedElsePrimary = collapsify == null, alwaysAlt = collapsify === "overlay";
    return alt2 ? alwaysAlt || altIfCollapsedElsePrimary && collapsed : alwaysPrimary || hideIfCollapsed || altIfCollapsedElsePrimary && !collapsed;
  }
  /**
   * Calling this {@link Core.mixin.Delayable#property-delayable-static} method marks this widget as dirty. The DOM
   * will be updated on the next animation frame:
   *
   * ```javascript
   *  widget.recompose();
   *
   *  console.log(widget.recompose.isPending);
   *  > true
   * ```
   *
   * A pending update can be flushed by calling `flush()` (this does nothing if no update is pending):
   *
   * ```javascript
   *  widget.recompose.flush();
   * ```
   *
   * This can be combined in one call to force a DOM update without first scheduling one:
   *
   * ```javascript
   *  widget.recompose.now();
   * ```
   *
   * @advanced
   */
  async recompose() {
    return this.recomposeInternal();
  }
  async recomposeInternal() {
    const me = this, options = {
      targetElement: me.element,
      domConfig: me.doCompose(),
      refOwner: me,
      callback: me.domSyncCallback,
      // This limits the sync() to only removing the classes and styles added by previous renderings. This
      // allows dynamically added styles and classes to be preserved:
      strict: true
    };
    if (me.transitionRecompose) {
      me.isTransitioningDom = true;
      await DomHelper.transition(ObjectHelper.assign({
        element: me.element,
        action() {
          DomSync.sync(options);
        }
      }, me.transitionRecompose));
      if (me.isDestroyed) {
        return;
      }
      me.isTransitioningDom = false;
      me.trigger("transitionedRecompose");
    } else {
      DomSync.sync(options);
    }
    if (options.changed) {
      me.afterRecompose();
      me.trigger("recompose");
    }
    me.resumeRecompose();
  }
  // To allow hooking into resuming recompose, used by TaskBoard
  resumeRecompose() {
    this.recompose.resume();
  }
  changeElementRef(el) {
    if (typeof el === "string") {
      const id2 = el;
      if (!(el = document.getElementById(id2))) {
        throw new Error(`No element found with id '${id2}'`);
      }
    }
    return el;
  }
  changeAppendTo(appendTo) {
    return this.changeElementRef(appendTo);
  }
  updateAppendTo(appendTo) {
    if (!this.isConfiguring && appendTo) {
      this.render();
    }
  }
  changeInsertBefore(insertBefore) {
    return this.changeElementRef(insertBefore);
  }
  updateInsertBefore(insertBefore) {
    if (!this.isConfiguring && insertBefore) {
      this.render();
    }
  }
  changeInsertFirst(insertFirst) {
    return this.changeElementRef(insertFirst);
  }
  updateInsertFirst(insertFirst) {
    if (!this.isConfiguring && insertFirst) {
      this.render();
    }
  }
  /**
   * Interprets the {@link #config-appendTo}, {@link #config-insertBefore} and {@link #config-insertFirst}
   * configs to return an array containing `[parentElement, insertBefore]`
   * @internal
   * @param {Core.widget.Widget} source The widget for which to ascertain its render context.
   * @returns {HTMLElement[]} The `[parentElement, insertBefore]` elements.
   */
  getRenderContext(config = this, renderTo) {
    let parentElement = renderTo || config.appendTo, { insertFirst, insertBefore } = config;
    if (insertFirst) {
      parentElement = insertFirst;
      insertBefore = parentElement.firstChild;
    }
    if (insertBefore) {
      parentElement = insertBefore.parentElement;
    }
    return [parentElement, insertBefore || void 0];
  }
  render(renderTo, triggerPaint = true) {
    var _a5;
    const me = this, { element } = me, [
      parentElement,
      insertBefore
    ] = me.getRenderContext(me, renderTo);
    me.emptyCache();
    if (me.syncElement && me.currentElement) {
      DomHelper.sync(element, me.currentElement);
    } else {
      parentElement == null ? void 0 : parentElement.insertBefore(element, insertBefore);
      me.currentElement = element;
    }
    if ((_a5 = element.parentElement) == null ? void 0 : _a5.closest(".b-widget")) {
      element.classList.remove(..._Widget.outerCls);
    }
    super.render(parentElement, triggerPaint);
    me.rendered = true;
    me.getConfig("role");
    if (triggerPaint) {
      me.getConfig("contentRange");
      me.triggerPaint();
    }
    me.setupFocusListeners();
  }
  /**
   * A function which, when passed an instance of this Widget, produces a valid HTML string which is compiled
   * to create the encapsulating element for this Widget, and its own internal DOM structure.
   *
   * Note that this just creates the DOM structure that *this* Widget owns. If it contains child widgets
   * (Such as for example a grid), this is not included. The template creates own structure.
   *
   * Certain elements within the generated element can be identified as special elements with a `reference="name"`
   * property. These will be extracted from the element upon creation and injected as the named property into
   * the Widget. For example, a {@link Core.widget.TextField} will have an `input` property which is its
   * `<input>` element.
   * @param {Core.widget.Widget} me The widget for which to produce the initial HTML structure.
   * @internal
   */
  template({ tag, html, htmlCls, name }) {
    const me = this;
    if (typeof html === "string" && callbackRe.test(html) && me.resolveCallback(html, me, false)) {
      html = me.callback(html, me, [me]);
    }
    const content = (html == null ? void 0 : html.call) ? html.call(me, me) : html;
    return `<${tag} class="${content ? htmlCls : ""}" ${name ? `data-name="${name}"` : ""}>${content || ""}</${tag}>`;
  }
  updateRecomposeAsync(async) {
    this.recompose.immediate = !async;
  }
  //endregion
  //---------------------------------------------------------------------------------------------------------
  onConfigChange({ name }) {
    var _a5;
    if (this._element && !this.isDestroying && ((_a5 = this.$meta.renderConfigs) == null ? void 0 : _a5[name])) {
      this.recompose();
    }
  }
  //region Extract config
  // These functions are not meant to be called by any code other than Base#getCurrentConfig()
  // Clean up configs
  preProcessCurrentConfigs(configs) {
    super.preProcessCurrentConfigs(configs);
    delete configs.parent;
  }
  // Extract config's current value, special handling for style
  getConfigValue(name, options) {
    if (name === "style") {
      return this._style;
    }
    return super.getConfigValue(name, options);
  }
  // Extract current value of all initially used configs, special handling for widget type
  getCurrentConfig(options) {
    const result = super.getCurrentConfig(options);
    if ((options == null ? void 0 : options.depth) > 0) {
      result.type = this.type;
    }
    return result;
  }
  //endregion
  /**
   * Get widgets elements dataset or assign to it
   * @property {Object}
   * @category DOM
   */
  get dataset() {
    var _a5;
    return ((_a5 = this.element) == null ? void 0 : _a5.dataset) || EMPTY;
  }
  changeDataset(dataset) {
    ObjectHelper.assign(this.dataset, dataset);
  }
  updateParent(parent) {
    const { _element: element } = this;
    if (element) {
      element.classList[parent ? "remove" : "add"](..._Widget.outerCls);
    }
  }
  get constrainTo() {
    let result = this._constrainTo;
    result = result === void 0 ? globalThis : (result == null ? void 0 : result.nodeType) === Node.DOCUMENT_FRAGMENT_NODE ? result.host || result.ownerDocument : result;
    if (this.positioned) {
      const { offsetParent } = this.element;
      if (offsetParent && DomHelper.getStyleValue(offsetParent, "overflow") === "hidden") {
        result = Rectangle.from(offsetParent).moveTo(0, 0);
      } else if (result && !result.isRectangle) {
        const isViewport = result === document || result === globalThis;
        result = Rectangle.from(result, offsetParent);
        if (isViewport) {
          result.translate(globalThis.pageXOffset, globalThis.pageYOffset);
        }
      }
    }
    return result;
  }
  updateCentered(value2) {
    const me = this, { element, _anchorElement } = me;
    if (value2 && !me.floating && !me.positioned) {
      throw new Error("`centered` is only relevant when a Widget is `floating` or `positioned`");
    }
    if (value2) {
      element.classList.add("b-centered");
      element.style.transform = element.style.left = element.style.insetInlineStart = element.style.right = element.style.top = "";
      _anchorElement == null ? void 0 : _anchorElement.classList.add("b-hide-display");
      element.classList.remove("b-anchored");
    } else {
      element.classList.remove("b-centered");
    }
  }
  /**
   * The child element into which content should be placed. This means where {@link #config-html} should be put,
   * or, for {@link Core.widget.Container}s, where child items should be rendered.
   * @property {HTMLElement}
   * @readonly
   * @category DOM
   * @advanced
   */
  get contentElement() {
    return this.element;
  }
  get contentRange() {
    const me = this, contentRange = me._contentRange || (me._contentRange = new Range());
    if (contentRange.collapsed) {
      if (me.initializingHtml && me.adopt) {
        contentRange.setStart(me.contentElement, 0);
      } else {
        contentRange.selectNodeContents(me.contentElement);
      }
    }
    return contentRange;
  }
  /**
   * This method fixes the element's `$refOwnerId` when this instance's `id` is changing.
   * @param {Node} el The element or DOM node to fix.
   * @param {String} id The new id being assigned.
   * @param {String} oldId The old id (previously assigned).
   * @private
   */
  fixRefOwnerId(el, id2, oldId) {
    if (el.$refOwnerId === oldId) {
      el.$refOwnerId = id2;
      for (let { childNodes } = el, i = childNodes.length; i-- > 0; ) {
        this.fixRefOwnerId(childNodes[i], id2, oldId);
      }
    }
  }
  get placement() {
    var _a5;
    const me = this, { element } = me;
    let adjRect, placement, rect;
    if ((element == null ? void 0 : element.offsetParent) && !nonFlowedPositions.test(DomHelper.getStyleValue(element, "position"))) {
      const next = element.nextElementSibling, previous = element.previousElementSibling, last = !next && previous;
      placement = DomHelper.getStyleValue(element.parentElement, "flex-direction");
      if (placement) {
        placement = placement.startsWith("row") ? "h" : "v";
      } else {
        adjRect = (_a5 = next || previous) == null ? void 0 : _a5.getBoundingClientRect();
        rect = adjRect && element.getBoundingClientRect();
        placement = adjRect && Math.abs(adjRect.top - rect.top) < Math.abs(adjRect.left - rect.left) ? "h" : "v";
      }
      placement += placement === "h" ? last ? "r" : "l" : last ? "b" : "t";
    }
    return placement;
  }
  updateId(id2, oldId) {
    super.updateId(id2, oldId);
    if (oldId) {
      const { byRef, element } = this;
      for (const ref in byRef) {
        byRef[ref].dataset && (byRef[ref].dataset.ownerCmp = id2);
      }
      element.id = id2;
      this.fixRefOwnerId(element, id2, oldId);
    }
  }
  /**
   * Get/set widgets elements style. The setter accepts a cssText string or a style config object, the getter always
   * returns a CSSStyleDeclaration
   * @property {CSSStyleDeclaration}
   * @accepts {String|Object|CSSStyleDeclaration}
   * @category DOM
   */
  get style() {
    const { element } = this;
    return (element == null ? void 0 : element.ownerDocument.defaultView.getComputedStyle(element)) || this._style;
  }
  updateStyle(style) {
    this.element && DomHelper.applyStyle(this.element, style);
  }
  updateTitle(title) {
    if (this.titleElement) {
      this.titleElement.innerHTML = title;
    }
  }
  //region floating
  // Hook used by Tooltip to handle RTL
  beforeAlignTo(spec) {
  }
  /**
   * If this Widget is {@link #config-floating} or {@link #config-positioned}, and visible, aligns the widget
   * according to the passed specification. To stop aligning, call this method without arguments.
   * @param {AlignSpec|HTMLElement} [spec] Alignment options. May be an alignment specification object, or an
   * `HTMLElement` to align to using this Widget's {@link #config-align} configuration.
   * @category Float & align
   */
  alignTo(spec) {
    var _a5, _b2;
    const me = this, {
      lastAlignSpec,
      element
    } = me, {
      offsetParent,
      style,
      classList
    } = element;
    if (lastAlignSpec) {
      lastAlignSpec.monitorIntersection && me.intersectionObserver.unobserve(lastAlignSpec.target);
      if (isFinite(lastAlignSpec.zone)) {
        element.classList.remove(alignedClass[lastAlignSpec.zone]);
      }
    }
    if (!spec) {
      me.removeTransientListeners();
      me.anchor = me.lastAlignSpec = null;
      return;
    }
    me.beforeAlignTo(spec);
    if (spec.nodeType === Element.ELEMENT_NODE || spec.isWidget || spec.$$name === "Point") {
      spec = {
        target: spec
      };
    }
    me.releaseSizeConstraints();
    const {
      scrollable,
      constrainTo
    } = me, elMinHeight = DomHelper.measureSize(DomHelper.getStyleValue(element, "minHeight"), element), elMinWidth = DomHelper.measureSize(DomHelper.getStyleValue(element, "minWidth"), element), positioned = me.positioned && DomHelper.getStyleValue(element, "position") !== "fixed", scale = me.scale || 1, passedTarget = spec.target, targetEvent = spec.realignEvent || getEventTarget(spec.domEvent || spec.event || (((_a5 = passedTarget == null ? void 0 : passedTarget.target) == null ? void 0 : _a5.nodeType) === Node.ELEMENT_NODE ? passedTarget : null)), aligningToEvent = ((_b2 = targetEvent == null ? void 0 : targetEvent.target) == null ? void 0 : _b2.nodeType) === Element.ELEMENT_NODE, target = aligningToEvent ? targetEvent.target : (passedTarget == null ? void 0 : passedTarget.isRectangle) ? passedTarget : (passedTarget == null ? void 0 : passedTarget.element) || passedTarget, aligningToElement = (target == null ? void 0 : target.nodeType) === Element.ELEMENT_NODE;
    spec = spec.realignTarget ? ObjectHelper.clone(spec) : ObjectHelper.merge({
      aligningToEvent,
      aligningToElement,
      constrainTo,
      align: "b-t",
      // we can end up w/o a value for this if an object replaces a string
      axisLock: me.axisLock,
      anchor: me.anchor
    }, me.align, spec);
    if (aligningToEvent) {
      spec.domEvent = targetEvent;
    }
    const minWidth = spec.minWidth || elMinWidth, minHeight = spec.minHeight || elMinHeight;
    me.lastAlignSpec = spec;
    if (aligningToElement) {
      spec.target = spec.anchoredTo = target;
      if (hasLayout(target instanceof SVGElement ? target.closest("svg") : target)) {
        spec = Object.create(spec);
        spec.target = me.lastAlignSpec.targetRect = spec.realignTarget || (spec.allowTargetOut ? Rectangle.from(target, positioned ? offsetParent : null, !positioned) : DomHelper.isInView(aligningToEvent ? targetEvent : target, false, me));
        if (!spec.target) {
          const result2 = me.hide();
          if (!me.isDestroyed) {
            me.lastAlignSpec.targetOutOfView = true;
          }
          return result2;
        }
        spec.target.height = Math.max(spec.target.height, 1);
        spec.target.width = Math.max(spec.target.width, 1);
      }
    }
    if (spec.anchor) {
      spec.anchorSize = me.anchorSize;
      if (!element.contains(me.anchorPathElement)) {
        element.appendChild(me.anchorElement);
      }
    }
    if (typeof spec.offset === "string") {
      const offsetEl = element.querySelector(spec.offset), offsetRect = Rectangle.from(offsetEl), offsetPt = offsetRect.getAlignmentPoint(spec.align), outerRect = Rectangle.from(element), outerPt = outerRect.getAlignmentPoint(spec.align);
      spec.offset = [outerPt[0] - offsetPt[0], outerPt[1] - offsetPt[1]];
    }
    me.isAligning = true;
    me.trigger("beforeAlign", spec);
    const myPosition = Rectangle.from(element, positioned ? offsetParent : null, true), {
      width: startWidth,
      height: startHeight
    } = myPosition;
    myPosition.isAlignRectangle = true;
    minWidth && (myPosition.minWidth = minWidth * scale);
    minHeight && (myPosition.minHeight = minHeight * scale);
    me.isAligning = false;
    spec.rtl = me.rtl;
    if (spec.align.includes("s") || spec.align.includes("e")) {
      if (me.rtl) {
        spec.align = spec.align.replace(/s/g, "r").replace(/e/g, "l");
      } else {
        spec.align = spec.align.replace(/s/g, "l").replace(/e/g, "r");
      }
    }
    const result = me.lastAlignSpec.result = myPosition.alignTo(spec);
    let { align, anchor, x, y, width, height, overlap } = result;
    me.lastAlignSpec.zone = result.zone;
    if (height != startHeight) {
      const shrunk = height < startHeight;
      me.alignmentChanges = me.alignmentChanges | (shrunk ? 1 : 2);
      style[me.alignedHeightStyle = alignChangeDims[me.alignmentChanges & 3]] = `${height / scale}px`;
      if (shrunk && !me._minHeight && elMinHeight) {
        style.minHeight = `${Math.min(height, elMinHeight) / scale}px`;
      }
      if (scrollable) {
        scrollable.overflowY = shrunk;
      }
    }
    if (width != startWidth) {
      const shrunk = width < startWidth;
      me.alignmentChanges = me.alignmentChanges | (shrunk ? 4 : 8);
      style[me.alignedWidthStyle = alignChangeDims[me.alignmentChanges & 12]] = `${width / scale}px`;
      if (shrunk && !me._minWidth && elMinWidth) {
        style.minWidth = `${Math.min(width, elMinWidth) / scale}px`;
      }
      if (scrollable) {
        scrollable.overflowX = shrunk;
      }
    }
    if (align && me.alignmentChanges) {
      const newRect = Rectangle.from(element, positioned ? offsetParent : null, true);
      spec.align = align;
      const newResult = me.lastAlignSpec.result = newRect.alignTo(spec);
      anchor = newResult.anchor;
      x = newResult.x;
      y = newResult.y;
      width = newResult.width;
      height = newResult.height;
    }
    if (!me.centered) {
      me.setXY(x, y);
    }
    if (!result.overlap && isFinite(result.zone)) {
      classList.add(alignedClass[result.zone]);
    }
    if (anchor == null ? void 0 : anchor.edge) {
      let { edge } = anchor;
      const { anchorElement, masked } = me, { style: anchorStyle } = anchorElement, { style: svgStyle } = anchorElement.firstChild, elRect = Rectangle.from(element), colorMatchPoint = [];
      if (edge === "top" || edge === "bottom") {
        colorMatchPoint[0] = anchor.x;
        colorMatchPoint[1] = edge === "top" ? 1 : elRect.height - 1;
      } else {
        colorMatchPoint[0] = edge === "left" ? 1 : elRect.width - 1;
        colorMatchPoint[1] = anchor.y;
      }
      anchorStyle.display = "none";
      masked && (masked.element.style.display = "none");
      let colourSource = DomHelper.childFromPoint(element, ...colorMatchPoint);
      if (((colourSource == null ? void 0 : colourSource.matches(":focus")) || colourSource === GlobalEvents_default.currentOverElement) && element.compareDocumentPosition(colourSource) & Node.DOCUMENT_POSITION_CONTAINED_BY) {
        colourSource = colourSource.parentNode;
      }
      if (colourSource && colourSource !== document) {
        let fillColour = DomHelper.getStyleValue(colourSource, "background-color");
        while (fillColour.match(isTransparent) && DomHelper.getStyleValue(colourSource, "position") !== "absolute") {
          colourSource = colourSource.parentNode;
          if (colourSource === document) {
            break;
          }
          fillColour = DomHelper.getStyleValue(colourSource, "background-color");
        }
        if (fillColour.match(isTransparent)) {
          me.anchorPathElement.setAttribute("fill", me.defaultAnchorBackgroundColor);
        } else {
          me.anchorPathElement.setAttribute("fill", fillColour);
        }
      }
      anchorStyle.transform = anchorStyle.display = "";
      masked && (masked.element.style.display = "");
      if (edge === "left" || edge === "right") {
        edge = edge === "left" ? me.rtl ? "end" : "start" : me.rtl ? "start" : "end";
      }
      anchorElement.className = `b-anchor b-anchor-${edge}`;
      anchor.x && (anchor.x /= scale);
      anchor.y && (anchor.y /= scale);
      svgStyle.left = `${anchor.x || 0}px`;
      svgStyle.translate = `0 ${anchor.y || 0}px`;
      classList.add("b-anchored");
    } else if (me._anchorElement) {
      me.anchorElement.classList.add("b-hide-display");
      classList.remove("b-anchored");
    }
    if (!aligningToElement) {
      style.pointerEvents = "none";
      const el = DomHelper.elementFromPoint(x, y);
      if (!me.owns(el)) {
        me.lastAlignSpec.anchoredTo = el;
      }
      style.pointerEvents = "";
    }
    if ((me.scrollAction === "realign" && aligningToElement || me.scrollAction === "hide") && !me.documentScrollListener) {
      me.clearTimeout("scrollListenerTimeout");
      if (spec.monitorTargetMutation && !me.targetObserver) {
        const targetObserver = me.targetObserver || (me.targetObserver = new MutationObserver(me.onTargetParentMutation.bind(me)));
        targetObserver.observe(DomHelper.getRootElement(target), {
          childList: true,
          attributes: true,
          subtree: true
        });
      }
      me.setTimeout(() => {
        const el = aligningToElement ? target : me.lastAlignSpec.anchoredTo, targetRoot = el ? DomHelper.getRootElement(el) : null;
        me.documentScrollListener = EventHelper.addListener({
          element: document,
          scroll: "doHideOrRealign",
          transitionend: "realignOnTransitionEnd",
          capture: true,
          thisObj: me
        });
        if (targetRoot == null ? void 0 : targetRoot.mode) {
          me.targetRootScrollListener = EventHelper.addListener({
            element: targetRoot,
            scroll: "doHideOrRealign",
            transitionend: "realignOnTransitionEnd",
            capture: true,
            thisObj: me
          });
        }
      }, me.scrollAction === "hide" ? me.ignoreScrollDuration : 0, "scrollListenerTimeout");
    }
    if (aligningToElement) {
      if (spec.monitorResize && !me.observedTargetEl) {
        ResizeMonitor.addResizeListener(target, me.onTargetResize);
        me.observedTargetEl = target;
      }
      if (spec.monitorIntersection && !(overlap || target.contains(element) || target.ownerSVGElement)) {
        me.intersectionObserver.observe(target);
      }
    }
    if (!BrowserHelper.isAndroid) {
      if (!me.observedConstrainEl) {
        const el = (constrainTo == null ? void 0 : constrainTo.isRectangle) ? globalThis : constrainTo;
        me.clearTimeout("resizeListenerTimeout");
        me.setTimeout(() => {
          me.observedConstrainEl = el || globalThis;
          ResizeMonitor.addResizeListener(me.observedConstrainEl, me.onAlignConstraintChange);
        }, me.ignoreScrollDuration, "resizeListenerTimeout");
      }
    }
  }
  onTargetParentMutation(mutationRecords) {
    var _a5;
    const { element, lastAlignSpec } = this;
    if (lastAlignSpec == null ? void 0 : lastAlignSpec.aligningToElement) {
      if (!((_a5 = lastAlignSpec.target) == null ? void 0 : _a5.isConnected)) {
        this.hide();
      } else if (mutationRecords.some(({ target }) => target.contains(lastAlignSpec.target) && !element.contains(target))) {
        this.realign();
      }
    }
  }
  get intersectionObserver() {
    return this._intersectionObserver || (this._intersectionObserver = new IntersectionObserver(this.onTargetIntersectionChange.bind(this), {
      root: BrowserHelper.isSafari ? this.rootElement : this.rootElement.ownerDocument
    }));
  }
  onTargetIntersectionChange(entries) {
    if (!this.isDestroyed) {
      const e = entries[entries.length - 1];
      if (!e.isIntersecting) {
        this.onAlignTargetOutOfView(e.target);
      }
    }
  }
  onTargetResize() {
    const { lastAlignSpec } = this;
    if (lastAlignSpec) {
      const {
        width: lastWidth,
        height: lastHeight
      } = lastAlignSpec.targetRect, {
        width,
        height
      } = lastAlignSpec.target.getBoundingClientRect();
      if (width !== lastWidth || height !== lastHeight) {
        this.onAlignConstraintChange(...arguments);
      }
    }
  }
  /**
   * This method is called when the {@link #function-alignTo} target element loses intersection with the
   * visible viewport. That means it has been scrolled out of view, or becomes zero size, or hidden or
   * is removed from the DOM.
   *
   * The base class implementation hides by default.
   * @param {HTMLElement} target The alignTo target that is no longer in view.
   * @internal
   */
  onAlignTargetOutOfView(target) {
    this.hide();
    this.lastAlignSpec && (this.lastAlignSpec.targetOutOfView = true);
  }
  onAlignConstraintChange(el, oldRect, { height }) {
    const { style } = this.contentElement, { overflowY } = style;
    this.setTimeout(this.realign, 50);
    if (oldRect && height > oldRect.height) {
      style.overflowY = "hidden";
      this.requestAnimationFrame(() => style.overflowY = overflowY);
    }
  }
  /**
   * Called when an element which affects the position of this Widget's
   * {@link #function-alignTo align target} scrolls so that this can realign.
   *
   * If the target has scrolled out of view, then this Widget is hidden.
   * @internal
   */
  realign() {
    const me = this, { lastAlignSpec } = me;
    if ((me.floating || me.positioned) && lastAlignSpec && me.isVisible) {
      if (lastAlignSpec.aligningToElement) {
        const insideTarget = lastAlignSpec.target.contains(this.element), realignTarget = DomHelper.isInView(lastAlignSpec.target, false, me);
        if (!lastAlignSpec.allowTargetOut && (!hasLayout(lastAlignSpec.target) || !realignTarget)) {
          me.hide();
          if (!me.isDestroyed) {
            me.lastAlignSpec.targetOutOfView = true;
          }
          return;
        }
        lastAlignSpec.realignEvent = lastAlignSpec.domEvent;
        lastAlignSpec.realignTarget = insideTarget || lastAlignSpec.aligningToEvent ? null : realignTarget;
      }
      DomHelper.addTemporaryClass(me.element, "b-realigning", me.realignTimeout, me);
      me.alignTo(lastAlignSpec);
    }
  }
  /**
   * Returns the specified bounding rectangle of this widget.
   * @param {'border'|'client'|'content'|'inner'|'outer'} [which='border'] By default, the rectangle returned is the
   * bounding rectangle that contains the `element` border. Pass any of these values to retrieve various rectangle:
   *  - `'border'` to get the {@link Core.helper.util.Rectangle#function-from-static border rectangle} (the default).
   *  - `'client'` to get the {@link Core.helper.util.Rectangle#function-client-static client rectangle}.
   *  - `'content'` to get the {@link Core.helper.util.Rectangle#function-content-static content rectangle}.
   *  - `'inner'` to get the {@link Core.helper.util.Rectangle#function-inner-static inner rectangle}.
   *  - `'outer'` to get the {@link Core.helper.util.Rectangle#function-outer-static outer rectangle}.
   * @param {HTMLElement|Core.widget.Widget} [relativeTo] Optionally, a parent element or widget in whose space to
   * calculate the Rectangle.
   * @param {Boolean} [ignorePageScroll=false] Use browser viewport based coordinates.
   * @returns {Core.helper.util.Rectangle}
   * @internal
   */
  rectangle(which, relativeTo, ignorePageScroll) {
    return this.rectangleOf("element", which, relativeTo, ignorePageScroll);
  }
  /**
   * Returns the specified bounding rectangle of the specified child `element` of this widget.
   * @param {String|HTMLElement} [element] The child element or its name.
   * @param {'border'|'client'|'content'|'inner'|'outer'} [which='border'] By default, the rectangle returned
   * is the bounding rectangle that contains the `element` border. Pass any of these values to retrieve various
   * rectangle:
   *  - `'border'` to get the {@link Core.helper.util.Rectangle#function-from-static border rectangle} (the default).
   *  - `'client'` to get the {@link Core.helper.util.Rectangle#function-client-static client rectangle}.
   *  - `'content'` to get the {@link Core.helper.util.Rectangle#function-content-static content rectangle}.
   *  - `'inner'` to get the {@link Core.helper.util.Rectangle#function-inner-static inner rectangle}.
   *  - `'outer'` to get the {@link Core.helper.util.Rectangle#function-outer-static outer rectangle}.
   * @param {HTMLElement|Core.widget.Widget} [relativeTo] Optionally, a parent element or widget in whose space to
   * calculate the Rectangle. If `element` is not `'element'`, then this defaults to the widget's primary element.
   * @param {Boolean} [ignorePageScroll=false] Use browser viewport based coordinates.
   * @returns {Core.helper.util.Rectangle}
   * @internal
   */
  rectangleOf(element, which, relativeTo, ignorePageScroll) {
    if (typeof which !== "string") {
      ignorePageScroll = relativeTo;
      relativeTo = which;
      which = "";
    } else if (which === "border") {
      which = "";
    }
    if (typeof relativeTo === "boolean") {
      ignorePageScroll = relativeTo;
      relativeTo = void 0;
    }
    if (element !== "element" && relativeTo === void 0) {
      relativeTo = this.element;
    }
    relativeTo = (relativeTo == null ? void 0 : relativeTo.isWidget) ? relativeTo.element : relativeTo;
    if (typeof element === "string") {
      element = this[element];
    }
    return Rectangle[which || "from"](element, relativeTo, ignorePageScroll);
  }
  releaseSizeConstraints() {
    const me = this, {
      scrollable,
      element,
      alignmentChanges
    } = me, { style } = element;
    if (!me.centered) {
      DomHelper.setTopInsetInlineStart(element, 0, 0);
    }
    if (alignmentChanges & 12) {
      DomHelper.setLength(element, me.alignedWidthStyle, me[`_last${StringHelper.capitalize(me.alignedWidthStyle)}`] || "");
      style.minWidth = me._minWidth || "";
      if (scrollable) {
        scrollable.overflowY = scrollable.config.overflowY;
      }
    }
    if (alignmentChanges & 3) {
      DomHelper.setLength(element, me.alignedHeightStyle, me[`_last${StringHelper.capitalize(me.alignedHeightStyle)}`] || "");
      style.minHeight = me._minHeight || "";
      if (scrollable) {
        scrollable.overflowX = scrollable.config.overflowX;
      }
    }
    me.alignmentChanges = 0;
  }
  /**
   * Only valid for {@link #config-floating} Widgets. Moves to the front of the visual stacking order.
   * @category Float & align
   */
  toFront() {
    var _a5;
    const { element } = this, parent = this.floating ? this.floatRoot : this.positioned ? element == null ? void 0 : element.parentNode : null, widgetsFrag = document.createDocumentFragment();
    if (this.containsFocus) {
      for (let followingEl = element.nextSibling, nextEl; followingEl; followingEl = nextEl) {
        nextEl = followingEl.nextSibling;
        if (parent.contains(followingEl) && ((_a5 = followingEl.matches) == null ? void 0 : _a5.call(followingEl, ".b-floating,.b-positioned")) && !this.owns(followingEl)) {
          widgetsFrag.appendChild(followingEl);
        }
      }
      widgetsFrag.childElementCount && parent.insertBefore(widgetsFrag, element);
    } else if (element.nextElementSibling) {
      parent.appendChild(element);
    }
  }
  //endregion
  //region Getters/setters
  updateRef(ref) {
    this.element.dataset.ref = ref;
  }
  /**
   * The child element which scrolls if any. This means the element used by the {@link #config-scrollable}.
   * @property {HTMLElement}
   * @readonly
   * @category DOM
   * @advanced
   */
  get overflowElement() {
    return this.contentElement;
  }
  get maxHeightElement() {
    return this.element;
  }
  changeAlign(align) {
    return typeof align === "string" ? { align } : align;
  }
  changeScrollable(scrollable, oldScrollable) {
    if (typeof scrollable === "boolean") {
      scrollable = {
        overflowX: scrollable,
        overflowY: scrollable
      };
    }
    if (scrollable) {
      scrollable.element = this.overflowElement;
      scrollable.widget = this;
      if (!scrollable.isScroller) {
        scrollable = oldScrollable ? oldScrollable.setConfig(scrollable) : new this.scrollerClass(scrollable);
      }
      scrollable.syncOverflowState();
    } else {
      oldScrollable == null ? void 0 : oldScrollable.destroy();
    }
    return scrollable;
  }
  get html() {
    if (this.isConfiguring) {
      return this.content || this._html;
    }
    return this.contentElement.innerHTML;
  }
  changeHtml(html) {
    if (html && typeof html === "object" && !DomHelper.isVueConfig(html) && !DomHelper.isReactElement(html) && !Array.isArray(html)) {
      html = [html];
    }
    return html;
  }
  updateHtml(html) {
    const me = this;
    me.noChildElements = typeof html === "string" && !htmlRe3.test(html);
    if (!me.initializingElement) {
      me.insertContent(html, !(me.initializingHtml && me.adopt));
    }
  }
  changeHtmlMutationObserver(htmlMutationObserver, was) {
    const me = this, { contentElement } = me;
    was == null ? void 0 : was.disconnect();
    if (htmlMutationObserver) {
      const result = new MutationObserver(() => {
        if (me.updatingHtml) {
          me.updatingHtml = false;
        } else {
          me._html = contentElement.innerHTML;
        }
      });
      result.observe(contentElement, htmlMutationObserver);
      return result;
    }
  }
  updateContent(html) {
    if (!this.initializingElement) {
      this.insertContent(html);
    }
  }
  insertContent(newContent, replace = false) {
    const me = this, {
      element,
      contentElement,
      htmlCls,
      contentRange,
      autoClose
    } = me, anchorEl = replace && contentElement === element && me._anchorElement;
    if (DomHelper.isReactElement(newContent)) {
      return DomHelper.handleReactElement(me, newContent);
    }
    if (DomHelper.isVueConfig(newContent)) {
      return DomHelper.handleVueContent(me, newContent);
    }
    me.updatingHtml = true;
    me.getConfig("htmlMutationObserver");
    if (anchorEl) {
      element.removeChild(anchorEl);
    }
    if (htmlCls) {
      htmlCls.values.forEach((value2) => element.classList[newContent ? "add" : "remove"](value2));
    }
    me.autoClose = false;
    if (newContent) {
      let done, newContentFragment;
      if (typeof newContent === "object") {
        newContentFragment = document.createDocumentFragment();
        if (Array.isArray(newContent)) {
          DomSync.sync({
            domConfig: {
              children: newContent,
              onlyChildren: true
            },
            targetElement: newContentFragment
          });
        } else {
          DomSync.sync({
            domConfig: {
              ...newContent,
              onlyChildren: true
            },
            targetElement: newContentFragment
          });
        }
      } else if (replace) {
        contentElement.innerHTML = newContent;
        done = true;
      } else {
        contentRange.deleteContents();
        contentRange.insertNode(contentRange.createContextualFragment(newContent));
        done = true;
      }
      if (!done) {
        if (replace) {
          contentElement.innerHTML = "";
          contentElement.appendChild(newContentFragment);
        } else {
          if (element.isConnected) {
            contentRange.insertNode(newContentFragment);
          } else {
            contentElement.insertBefore(newContentFragment, contentElement.firstChild);
          }
        }
      }
    } else {
      if (replace || !element.isConnected) {
        contentElement.innerHTML = "";
      } else {
        contentRange.deleteContents();
      }
    }
    me.autoClose = autoClose;
    if (anchorEl) {
      element.appendChild(anchorEl);
    }
    me.contentRangeStartOffset = contentRange.startOffset;
    me.contentRangeEndOffset = contentRange.endOffset;
    contentElement.classList.toggle("b-no-child-elements", me.noChildElements);
    if (me.isComposable) {
      me.recompose();
    }
    if ((me.floating || me.positioned) && !me.isAligning) {
      me.realign();
    }
  }
  onThemeChange() {
    var _a5;
    this._anchorSize = null;
    if ((_a5 = this.anchorElement) == null ? void 0 : _a5.offsetParent) {
      this.realign();
    }
  }
  /**
   * Returns an `[x, y]` array containing the width and height of the anchor arrow used when
   * aligning this Widget to another Widget or element.
   *
   * The height is the height of the arrow when pointing upwards, the width is the width
   * of the baseline.
   * @property {Number[]}
   * @category Float & align
   */
  get anchorSize() {
    const me = this;
    let result = this._anchorSize;
    if (!result) {
      const borderWidth = parseFloat(DomHelper.getStyleValue(me.element, "border-top-width")), borderColour = DomHelper.getStyleValue(me.element, "border-top-color"), anchorElement = me.anchorElement, { className } = anchorElement, svgEl = anchorElement.firstElementChild, pathElement = me.anchorPathElement = svgEl.lastElementChild, hidden = me._hidden;
      anchorElement.className = "b-anchor b-anchor-top";
      let backgroundColour = DomHelper.getStyleValue(me.contentElement, "background-color");
      if (backgroundColour.match(isTransparent)) {
        backgroundColour = DomHelper.getStyleValue(me.element, "background-color");
      }
      me.defaultAnchorBackgroundColor = backgroundColour;
      result = svgEl.getBoundingClientRect();
      const [width, height] = result = me._anchorSize = [result.width, result.height];
      anchorElement.className = className;
      svgEl.setAttribute("height", height + borderWidth);
      svgEl.setAttribute("width", width);
      pathElement.setAttribute("d", `M0,${height}L${width / 2},0.5L${width},${height}`);
      if (borderWidth) {
        pathElement.setAttribute("stroke-width", borderWidth);
        pathElement.setAttribute("stroke", borderColour);
      }
      result[1] -= borderWidth;
      hidden && me.syncHiddenCls();
      if (!me.themeChangeListener) {
        me.themeChangeListener = GlobalEvents_default.ion({
          theme: "onThemeChange",
          thisObj: me
        });
      }
      me.anchorPathElement.setAttribute("fill", me.defaultAnchorBackgroundColor);
    }
    return result;
  }
  get anchorElement() {
    const me = this;
    if (!me._anchorElement) {
      const useFilter = me.floating || me.positioned, filterId = `${me.id}-shadow-filter`;
      me._anchorElement = DomHelper.createElement({
        parent: me.element,
        className: "b-anchor b-anchor-top",
        children: [{
          tag: "svg",
          ns: "http://www.w3.org/2000/svg",
          version: "1.1",
          class: "b-pointer-el",
          children: [useFilter ? {
            tag: "defs",
            children: [{
              tag: "filter",
              id: filterId,
              children: [{
                tag: "feDropShadow",
                dx: 0,
                dy: -1,
                stdDeviation: 1,
                "flood-opacity": 0.2
              }]
            }]
          } : null, {
            tag: "path",
            [useFilter ? "filter" : ""]: `url(#${filterId})`
          }]
        }]
      });
    }
    return me._anchorElement;
  }
  updateAnchor(anchor) {
    var _a5;
    (_a5 = this._anchorElement) == null ? void 0 : _a5.classList.toggle("b-hide-display", !anchor);
  }
  changeDraggable(draggable, was) {
    var _a5;
    const me = this;
    (_a5 = was == null ? void 0 : was.destroy) == null ? void 0 : _a5.call(was);
    if (draggable) {
      const config = typeof draggable === "object" ? draggable : {};
      me.draggableHandleSelector = config.handleSelector;
      delete config.handleSelector;
      draggable = new DragHelper(ObjectHelper.merge({
        name: "widget",
        target: me,
        floatRootOwner: me,
        rtlSource: me,
        positioning: me.floating ? "inset" : "",
        dragWithin: me.floating ? void 0 : me.element.parentNode,
        internalListeners: {
          beforeDragStart: me.onBeforeDragStart,
          dragStart: me.onWidgetDragStart,
          thisObj: me
        }
      }, config));
    }
    return draggable;
  }
  onBeforeDragStart() {
    const me = this;
    if (me.validateDragStartEvent(me.draggable.startEvent)) {
      const { x, y, element } = me;
      element.classList.remove("b-centered");
      me.alignTo();
      if (me.centered) {
        DomHelper.setTopInsetInlineStart(element, y, x);
      }
      return true;
    }
    return false;
  }
  /**
   * Validates a `dragstart` event with respect to the target element. Dragging is not normally
   * initiated when the target is interactive such as an input field or its label, or a button.
   * This may be overridden to provide custom drag start validation.
   * @param {DragEvent} e The `dragstart` event to validate.
   * @returns {Boolean} Return `true` if the drag is to be allowed.
   * @internal
   */
  validateDragStartEvent(e) {
    var _a5;
    const me = this, actualTarget = DomHelper.elementFromPoint(e.clientX, e.clientY), { draggableHandleSelector, draggableBlacklistSelector } = me;
    if (draggableBlacklistSelector && actualTarget.closest(draggableBlacklistSelector)) {
      return false;
    }
    if (draggableHandleSelector) {
      const blacklist = (_a5 = negationPseudo.exec(draggableHandleSelector)) == null ? void 0 : _a5[1];
      if (blacklist && actualTarget.closest(blacklist)) {
        return false;
      } else if (!actualTarget.closest(`${draggableHandleSelector}`)) {
        return false;
      }
    }
    return true;
  }
  setStyle(name, value2) {
    DomHelper.applyStyle(this.element, ObjectHelper.isObject(name) ? name : { [name]: value2 });
    return this;
  }
  changeFloating(value2) {
    return Boolean(value2);
  }
  changePositioned(value2) {
    return Boolean(value2);
  }
  updatePositionable(positionable) {
    this.element.classList.toggle("b-positionable", Boolean(positionable));
    if (positionable) {
      this.innerItem = false;
    }
  }
  updatePositioned(positioned) {
    const { element } = this;
    element.classList.toggle("b-positioned", Boolean(positioned));
    if (positioned) {
      this.innerItem = false;
    }
    element[positioned ? "setAttribute" : "removeAttribute"]("tabindex", positioned ? "-1" : void 0);
  }
  getXY() {
    const { element } = this;
    return [DomHelper.getPageX(element), DomHelper.getPageY(element)];
  }
  /**
   * Moves this Widget to the x,y position. Both arguments can be omitted to just set one value.
   *
   * *For {@link #config-floating} Widgets, this is a position in the browser viewport.*
   * *For {@link #config-positioned} Widgets, this is a position in the element it was rendered into.*
   *
   * @param {Number} [x]
   * @param {Number} [y]
   * @category Float & align
   */
  setXY(x, y) {
    const me = this, { element } = me;
    if (me.floating || me.positioned) {
      if (x != null) {
        me._x = x;
      } else {
        x = me._x;
      }
      if (y != null) {
        me._y = y;
      } else {
        y = me._y;
      }
      if (DomHelper.getStyleValue(element, "position") === "fixed") {
        const r = element.getBoundingClientRect(), [cx, cy] = DomHelper.getTranslateXY(element), xDelta = x - r.x, yDelta = y - r.y;
        DomHelper.setTranslateXY(element, cx + xDelta, cy + yDelta);
      } else if (me.rtl) {
        DomHelper.setTopInsetInlineStart(element, y, element.offsetParent.offsetWidth - element.offsetWidth - x - 1);
      } else {
        DomHelper.setTopInsetInlineStart(element, y, x);
      }
      if (me.isConstructing) {
        me.centered = false;
      } else {
        element.classList.remove("b-centered");
      }
    }
  }
  setPos(name, value2) {
    const me = this, { positionable } = me;
    [name, value2] = DomHelper.unitize(name, value2 != null ? value2 : "");
    me.element.style[name] = value2;
    if (me.top == null && me.right == null && me.bottom == null && me.left == null) {
      if (positionable === "after" || positionable === "before") {
        me.positionable = null;
      }
    } else if (!positionable) {
      me.positionable = "before";
    }
  }
  updateBottom(bottom) {
    this.setPos("bottom", bottom);
  }
  updateLeft(left) {
    this.setPos("inset-inline-start", left);
  }
  updateRight(right) {
    this.setPos("inset-inline-end", right);
  }
  updateTop(top) {
    this.setPos("top", top);
  }
  /**
   * Moves this Widget to the desired x position.
   *
   * Only valid if this Widget is {@link #config-floating} and not aligned or anchored to an element.
   * @property {Number}
   * @category Float & align
   */
  get x() {
    return this.getXY()[0];
  }
  changeX(x) {
    this.setXY(x);
  }
  /**
   * Moves this Widget to the desired y position.
   *
   * Only valid if this Widget is {@link #config-floating} and not aligned or anchored to an element.
   * @property {Number}
   * @category Float & align
   */
  get y() {
    return this.getXY()[1];
  }
  changeY(y) {
    this.setXY(null, y);
  }
  /**
   * Get elements offsetWidth or sets its style.width, or specified width if element not created yet.
   * @property {Number}
   * @accepts {Number|String}
   * @category Layout
   */
  get width() {
    const me = this, element = me.element;
    if (me.monitorResize) {
      if (me._width == null) {
        me._width = element.offsetWidth;
      }
      return me._width;
    }
    return element.offsetWidth;
  }
  changeWidth(width) {
    const me = this;
    DomHelper.setLength(me.element, "width", width);
    me._lastWidth = width;
    me._flex = null;
    me.element.style.flex = "";
    if (me.monitorResize) {
      me._oldWidth = me._width;
      me._width = null;
    } else {
      return width;
    }
  }
  // This method is used by State API to drop cached width early to not rely on ResizeMonitor
  clearWidthCache() {
    this._width = null;
  }
  /**
   * Get/set elements maxWidth. Getter returns max-width from elements style, which is always a string. Setter accepts
   * either a String or a Number (which will have 'px' appended). Note that like {@link #config-width},
   * _reading_ the value will return the numeric value in pixels.
   * @property {String}
   * @accepts {String|Number}
   * @category Layout
   */
  get maxWidth() {
    return DomHelper.measureSize(this.element.style.maxWidth, this.element);
  }
  updateMaxWidth(maxWidth) {
    this._lastMaxWidth = maxWidth;
    DomHelper.setLength(this.element, "maxWidth", maxWidth);
  }
  /**
   * Get/set elements minWidth. Getter returns min-width from elements style, which is always a string. Setter accepts
   * either a String or a Number (which will have 'px' appended). Note that like {@link #config-width},
   * _reading_ the value will return the numeric value in pixels.
   * @property {String}
   * @accepts {String|Number}
   * @category Layout
   */
  get minWidth() {
    return DomHelper.measureSize(this.element.style.minWidth, this.element);
  }
  updateMinWidth(minWidth) {
    DomHelper.setLength(this.element, "minWidth", minWidth);
  }
  updateFlex(flex) {
    this.getConfig("width");
    if (typeof flex === "number" || !isNaN(flex)) {
      flex = `${flex} ${flex}`;
    }
    this.element.style.flex = flex;
    this.element.style.width = "";
  }
  updateAlignSelf(alignSelf) {
    this.element.style.alignSelf = alignSelf;
  }
  updateMargin(margin) {
    this.element.style.margin = this.parseTRBL(margin).join(" ");
  }
  updateTextAlign(align, oldAlign) {
    oldAlign && this.element.classList.remove(`b-text-align-${oldAlign}`);
    this.element.classList.add(`b-text-align-${align}`);
  }
  /**
   * Get element's offsetHeight or sets its style.height, or specified height if element no created yet.
   * @property {Number}
   * @accepts {Number|String}
   * @category Layout
   */
  get height() {
    const me = this, element = me.element;
    if (me.monitorResize) {
      if (me._height == null) {
        me._height = element.offsetHeight;
      }
      return me._height;
    }
    return element.offsetHeight;
  }
  changeHeight(height) {
    DomHelper.setLength(this.element, "height", height);
    this._lastHeight = height;
    this._height = null;
  }
  /**
   * Get/set element's maxHeight. Getter returns max-height from elements style, which is always a string. Setter
   * accepts either a String or a Number (which will have 'px' appended). Note that like {@link #config-height},
   * _reading_ the value will return the numeric value in pixels.
   * @property {String}
   * @accepts {String|Number}
   * @category Layout
   */
  get maxHeight() {
    return DomHelper.measureSize(this.maxHeightElement.style.maxHeight, this.element);
  }
  updateMaxHeight(maxHeight) {
    this._lastMaxHeight = maxHeight;
    DomHelper.setLength(this.maxHeightElement, "maxHeight", maxHeight);
  }
  /**
   * Get/set element's minHeight. Getter returns min-height from elements style, which is always a string. Setter
   * accepts either a String or a Number (which will have 'px' appended). Note that like {@link #config-height},
   * _reading_ the value will return the numeric value in pixels.
   * @property {String}
   * @accepts {String|Number}
   * @category Layout
   */
  get minHeight() {
    return DomHelper.measureSize(this.element.style.minHeight, this.element);
  }
  updateMinHeight(minHeight) {
    DomHelper.setLength(this.element, "minHeight", minHeight);
  }
  updateDisabled(disabled = false, was) {
    const me = this, { element, focusElement, ariaElement } = me;
    me.trigger("beforeUpdateDisabled", { disabled });
    if (disabled) {
      me.revertFocus();
      if (!me._disabled !== !disabled) {
        return;
      }
    }
    if (element) {
      element.classList[disabled ? "add" : "remove"]("b-disabled");
      if (disabled === "inert") {
        element.inert = true;
      } else if (was === "inert") {
        element.inert = false;
      }
      if (focusElement) {
        focusElement.disabled = disabled;
      }
      if (ariaElement) {
        ariaElement.setAttribute("aria-disabled", Boolean(disabled));
      }
    }
    me.onDisabled(disabled);
  }
  /**
   * Called when disabled state is changed.
   * Override in subclass that needs special handling when being disabled.
   * @param {Boolean} disabled current state
   * @private
   */
  onDisabled(disabled) {
  }
  /**
   * Disable the widget
   */
  disable() {
    this.disabled = true;
  }
  /**
   * Enable the widget
   */
  enable() {
    this.disabled = false;
  }
  /**
   * Requests fullscreen display for this widget
   * @returns {Promise} A Promise which is resolved with a value of undefined when the transition to full screen is complete.
   */
  requestFullscreen() {
    const me = this, result = Fullscreen.request(me.floating ? me.floatRoot : me.element);
    Fullscreen.onFullscreenChange(me.onFullscreenChange);
    me.element.classList.add("b-fullscreen");
    return result;
  }
  /**
   * Exits fullscreen mode
   * @returns {Promise} A Promise which is resolved once the user agent has finished exiting full-screen mode
   */
  exitFullscreen() {
    return Fullscreen.exit();
  }
  onFullscreenChange() {
    if (!Fullscreen.isFullscreen) {
      this.onExitFullscreen();
    }
  }
  onExitFullscreen() {
    Fullscreen.unFullscreenChange(this.onFullscreenChange);
    this.element.classList.remove("b-fullscreen");
  }
  /**
   * Get/set a tooltip on the widget. Accepts a string or tooltip config (specify true (or 'true') to use placeholder
   * as tooltip). When using a string it will configure the tooltip with `textContent: true` which enforces a default
   * max width.
   *
   * By default, this uses a singleton Tooltip instance which may be accessed from the `{@link Core.widget.Widget}`
   * class under the name `Widget.tooltip`. This is configured according to the config object on pointer over.
   *
   * To request a separate instance be created just for this widget, add `newInstance : true` to the configuration.
   *
   * @property {String|TooltipConfig}
   * @category Misc
   */
  get tooltip() {
    var _a5;
    const me = this;
    if (me._tooltip) {
      return me._tooltip;
    } else {
      const tooltip = (_a5 = _Widget.Tooltip) == null ? void 0 : _a5.getSharedTooltip(me.rootElement, me.eventRoot);
      if (tooltip && tooltip.activeTarget === me._element && tooltip.isVisible) {
        return tooltip;
      }
    }
  }
  //noinspection JSAnnotator
  changeTooltip(tooltip, oldTooltip) {
    const me = this, { element } = me;
    let result;
    if (tooltip) {
      if (!(me.preventTooltipOnTouch && BrowserHelper.isTouchDevice)) {
        const needNewInstance = (oldTooltip == null ? void 0 : oldTooltip.isTooltip) || tooltip.newInstance || tooltip.items || tooltip.bbar || tooltip.tbar;
        if (!tooltip.isTooltip && tooltip.constructor.name !== "Object") {
          tooltip = {
            html: typeof tooltip === "string" ? tooltip : me.placeholder,
            textContent: true
          };
        }
        if (needNewInstance) {
          tooltip.type = "tooltip";
          if (!tooltip.forElement) {
            tooltip.forElement = element;
          }
          if (!("showOnHover" in tooltip) && !tooltip.forSelector) {
            tooltip.showOnHover = true;
          }
          if (!("autoClose" in tooltip)) {
            tooltip.autoClose = true;
          }
          result = tooltip = _Widget.reconfigure(oldTooltip, tooltip, me);
          me.detachListeners("tooltipValueListener");
          if (!me.configureAriaDescription) {
            tooltip.ion({
              name: "tooltipValueListener",
              innerHtmlUpdate: "onTooltipValueChange",
              thisObj: me
            });
          }
        } else {
          element.dataset.btip = true;
          me.tipConfig = tooltip;
        }
      }
    } else {
      if (oldTooltip) {
        if (tooltip == null && oldTooltip.isTooltip) {
          oldTooltip.destroy();
        } else {
          oldTooltip.html = null;
          return;
        }
      } else {
        delete element.dataset.btip;
      }
    }
    if (tooltip && !me.ariaLabel && me.tooltipText) {
      element.setAttribute("aria-label", me.localizeText(me.tooltipText));
    }
    return result;
  }
  /**
   * The shared {@link Core.widget.Tooltip} instance which handles
   * {@link Core.widget.Widget#config-tooltip tooltips} which are __not__ configured
   * with `newInstance : true`.
   * @member {Core.widget.Tooltip} tooltip
   * @readonly
   * @static
   */
  // This property is defined in the Tooltip module but must be documented here.
  // If our tooltip is dynamic, then we must update our aria-describedBy whenever it changes.
  onTooltipValueChange({ value: value2, source }) {
    this.ariaDescription = typeof value2 == "string" ? value2 : source.contentElement.innerText;
  }
  get tooltipText() {
    const tooltip = this._tooltip;
    if (tooltip) {
      return tooltip.isTooltip ? tooltip.contentElement.innerText : typeof tooltip === "string" ? tooltip : tooltip.html;
    } else if (this.tipConfig) {
      return this.tipConfig.html;
    }
  }
  /**
   * Determines visibility by checking if the Widget is hidden, or any ancestor is hidden and that it has an
   * element which is visible in the DOM
   * @property {Boolean}
   * @category Visibility
   * @readonly
   */
  get isVisible() {
    const me = this;
    let element;
    return Boolean(
      !me._hidden && (element = this.element) && !me.isDestroying && element.isConnected && (!me.requireSize || hasLayout(element)) && (!me.parent || me.parent.isVisible)
    );
  }
  get isVisibleForValidation() {
    const me = this, { element } = me;
    let owner;
    return Boolean(
      element && (!me._hidden || me.tab && !me.tab.hidden) && !me.isDestroying && element.isConnected && //(!me.requireSize || hasLayout(element)) &&
      (!(owner = me.containingWidget) || owner.isVisibleForValidation)
    );
  }
  // If the callback is a function (instead of the name of the function),
  // the id must be converted into string format to be used as key for `toCallWhenVisible` map element
  whenVisible(callback, thisObj = this, args, id2 = callback.toString()) {
    var _a5;
    const me = this;
    if (me.isVisible && me.isPainted) {
      (_a5 = me.toCallWhenVisible) == null ? void 0 : _a5.delete(id2);
      me.callback(callback, thisObj, args);
    } else {
      (me.toCallWhenVisible || (me.toCallWhenVisible = /* @__PURE__ */ new Map())).set(id2, { callback, thisObj, args });
    }
  }
  /**
   * Focuses this widget if it has a focusable element.
   */
  focus() {
    var _a5;
    (_a5 = this.focusableElement) == null ? void 0 : _a5.focus({ preventScroll: true });
  }
  /**
   * Returns an object describing the focus and keyboard navigation aspects of this widget's {@link #property-focusElement}.
   * @property {Focusability}
   * @readonly
   * @category DOM
   * @internal
   */
  get focusability() {
    return DomHelper.getFocusability(this.focusElement);
  }
  /**
   * Returns this widget's {@link #property-focusElement} if that element can currently be given focus (e.g., this
   * widget is not disabled, or hidden).
   * @property {HTMLElement}
   * @readonly
   * @category DOM
   * @advanced
   */
  get focusableElement() {
    const me = this, focusElement = !me.isDestroying && me.isVisible && !me.disabled && me.focusElement;
    return (focusElement === document.body || (focusElement == null ? void 0 : focusElement.offsetParent)) && focusElement;
  }
  /**
   * Get this widget's primary focus holding element if this widget is itself focusable, or contains focusable widgets.
   * @property {HTMLElement}
   * @readonly
   * @category DOM
   * @advanced
   */
  get focusElement() {
  }
  get isFocusable() {
    return Boolean(this.focusableElement);
  }
  get isTabbable() {
    return this.focusability.tabbable;
  }
  set isTabbable(value2) {
    const { focusability, focusElement } = this, [add, rem] = focusability.natural ? [!value2 && "-1", value2] : [value2 && "0", !value2];
    if (focusElement && !value2 !== !focusability.tabbable) {
      rem && focusElement.removeAttribute("tabIndex");
      add && focusElement.setAttribute("tabIndex", add);
    }
  }
  /**
   * Shows this widget
   * @param {Object} [options] modifications to the show operation
   * @param {AlignSpec} [options.align] An alignment specification as passed to {@link #function-alignTo}
   * @param {Boolean} [options.animate=true] Specify as `false` to omit the {@link #config-showAnimation}
   * @category Visibility
   * @returns {Promise} A promise which is resolved when the widget is shown
   */
  async show({ align, animate = true } = {}) {
    var _a5;
    const me = this, {
      element,
      floating
    } = me, { style } = element, showAnimation = animate && me.showAnimation;
    let styleProp, animProps, trigger = !me.isVisible;
    if (trigger) {
      trigger = me.trigger("beforeShow");
      if (ObjectHelper.isPromise(trigger)) {
        trigger = await trigger;
      }
    }
    if (trigger !== false && ((_a5 = me.internalBeforeShow) == null ? void 0 : _a5.call(me)) !== false) {
      return new Promise((resolve) => {
        me.cancelHideShowAnimation();
        me.updateCentered(me._centered);
        if (floating) {
          const floatRoot = me.floatRoot;
          if (!floatRoot.contains(element)) {
            if (me.rendered) {
              floatRoot.appendChild(me.element);
            } else {
              me.render(floatRoot, false);
            }
          }
          if (style.transform.includes("scale")) {
            me.scale = null;
            style.transform = style.transformOrigin = "";
          }
          const scaledAncestor = me.closest(isScaled);
          if (scaledAncestor) {
            const { scale } = scaledAncestor;
            me.scale = scale;
            style.transform = `scale(${scale})`;
            style.transformOrigin = `0 0`;
          }
        }
        me._hidden = false;
        me.syncHiddenCls();
        if (floating && !isVisible(element)) {
          me.hide(false);
          return resolve();
        }
        me.onConfigChange({
          name: "hidden",
          value: false,
          was: true,
          config: me.$meta.configs.hidden
        });
        me.afterShow(align, !showAnimation ? resolve : null);
        if (showAnimation) {
          styleProp = Object.keys(showAnimation)[0];
          animProps = showAnimation[styleProp];
          const currentAnimation = me.currentAnimation = {
            showing: true,
            styleProp,
            resolve
          };
          me.isAnimating = true;
          style.transition = "none";
          style[styleProp] = animProps.from;
          DomHelper.getStyleValue(element, styleProp);
          me.currentAnimation.detacher = EventHelper.onTransitionEnd({
            element,
            property: styleProp,
            duration: parseDuration(animProps.duration) + 20,
            handler: () => me.afterHideShowAnimate(currentAnimation),
            thisObj: me
          });
          style.transition = `${styleProp} ${animProps.duration} ease ${animProps.delay}`;
          style[styleProp] = animProps.to;
        }
      });
    } else {
      return Promise.resolve();
    }
  }
  /**
   * Show aligned to another target element or {@link Core.widget.Widget} or {@link Core.helper.util.Rectangle}
   * @param {AlignSpec|HTMLElement|Number[]} align Alignment specification, or the element to align to using the
   * configured {@link #config-align}.
   * @category Float & align
   * @returns {Promise} A promise which is resolved when the widget is shown
   */
  async showBy(align, yCoord, options) {
    const me = this, isArray4 = Array.isArray(align);
    if (isArray4 || typeof align === "number") {
      const xy2 = isArray4 ? align : [align, yCoord];
      align = Object.assign({
        target: new Point(xy2[0] + 1, xy2[1] + 1),
        // Override any matchSize that we might have in our align config.
        // Otherwise we are going to be 1px wide/high
        matchSize: false,
        align: "t0-b0"
      }, isArray4 ? yCoord : options);
    }
    me.requireSize = true;
    if (me.isVisible) {
      DomHelper.addTemporaryClass(me.element, "b-realigning", 300, me);
      me.alignTo(align);
    } else {
      return me.show({ align });
    }
  }
  afterShow(align, resolveFn) {
    var _a5, _b2;
    const me = this;
    me.trigger("show");
    me._configuredAnchorState = me.anchor;
    (_b2 = (_a5 = me.owner) == null ? void 0 : _a5.onChildShow) == null ? void 0 : _b2.call(_a5, me);
    me.triggerPaint();
    if (me.floating || me.positioned) {
      if (align) {
        me.alignTo(align);
      } else if (me.centered) {
        me.alignTo({
          target: me.constrainTo,
          align: "c-c"
        });
      } else if (me.forElement) {
        me.alignTo(me.forElement);
      }
    }
    resolveFn == null ? void 0 : resolveFn();
  }
  onChildHide(hidden) {
    const { ariaElement } = this, { ownersRole } = hidden;
    if (hidden.floating && ariaElement) {
      if (ownersRole === "tooltip") {
        const { titleElement } = hidden, titleId = titleElement && DomHelper.getId(titleElement), id2 = DomHelper.getId(hidden.contentElement);
        if (titleId) {
          DomHelper.removeAttributeValue(ariaElement, "aria-describedby", titleId);
        }
        DomHelper.removeAttributeValue(ariaElement, "aria-describedby", id2);
      } else {
        this.ariaHasPopup = null;
        DomHelper.removeAttributeValue(ariaElement, "aria-controls", DomHelper.getId(hidden.ariaElement || hidden.element));
      }
    }
  }
  onChildShow(shown) {
    const { ariaElement } = this, { ownersRole } = shown;
    if (shown.floating && ariaElement) {
      if (ownersRole === "tooltip") {
        const { titleElement } = shown, titleId = titleElement && DomHelper.getId(titleElement), id2 = DomHelper.getId(shown.contentElement);
        if (titleId) {
          DomHelper.addAttributeValue(ariaElement, "aria-describedby", titleId);
        }
        DomHelper.addAttributeValue(ariaElement, "aria-describedby", id2);
      } else {
        this.ariaHasPopup = ownersRole;
        DomHelper.addAttributeValue(ariaElement, "aria-controls", DomHelper.getId(shown.ariaElement || shown.element));
      }
    }
  }
  triggerPaint() {
    var _a5;
    const me = this, { element, toCallWhenVisible } = me, firstPaint = !me.isPainted;
    if (me.isVisible) {
      if (firstPaint) {
        me.getConfig("scrollable", me.floating || me.positioned ? "draggable" : null);
        if (me.scaleToFitWidth && !me.monitorResize) {
          me.onParentElementResize = me.onParentElementResize.bind(me);
          ResizeMonitor.addResizeListener(element.parentElement, me.onParentElementResize);
          me.updateScale();
        }
        if (!me.hideBryntumDomMessage && (me.isTaskBoardBase || me.isGridBase || me.isCalendar)) {
          element.insertBefore(new Comment("POWERED BY BRYNTUM (https://bryntum.com)"), element.firstChild);
        }
        if (me.onConnectedCallback && globalThis.ResizeObserver) {
          me.connectedObserver = new ResizeObserver(() => {
            if (me.isElementConnected && !element.isConnected) {
              me.onConnectedCallback(false);
              me.isElementConnected = false;
            } else if (!me.isElementConnected && element.isConnected) {
              me.onConnectedCallback(true, me.isElementConnected == null);
              me.isElementConnected = true;
            }
          });
          me.connectedObserver.observe(element);
        }
      }
      me.eachWidget(widgetTriggerPaint, false);
      if (firstPaint) {
        me.getConfig("tooltip");
        if (!_Widget.Ripple && _Widget.RippleClass) {
          _Widget.Ripple = new _Widget.RippleClass({
            rootElement: me.rootElement
          });
        }
        if (globalThis.DocsBrowser && me.floating && me.closest((w) => {
          var _a6;
          return (_a6 = w.element) == null ? void 0 : _a6.closest(".fiddlePanelResult,.b-owned-by-fiddle");
        })) {
          element.classList.add("b-owned-by-fiddle");
        }
      }
      me.isPainted = true;
      firstPaint && me.triggerConfigs("paint");
      me.onInternalPaint({ firstPaint });
      me.trigger("paint", { firstPaint });
      (_a5 = me.afterInternalPaint) == null ? void 0 : _a5.call(me, { firstPaint });
      if (toCallWhenVisible == null ? void 0 : toCallWhenVisible.size) {
        for (const { callback, thisObj, args } of toCallWhenVisible.values()) {
          me.callback(callback, thisObj, args);
        }
        toCallWhenVisible.clear();
      }
      if (firstPaint) {
        if (me.monitorResize && !me.scaleToFitWidth) {
          ResizeMonitor.onElementResize([{ target: element }]);
        }
      }
      me.hasPainted = true;
    }
  }
  onInternalPaint() {
  }
  cancelHideShowAnimation() {
    const me = this, { currentAnimation, element } = me;
    if (currentAnimation) {
      me.isAnimating = false;
      if (element.classList.contains("b-hiding")) {
        element.classList.remove("b-hiding");
        me._hidden = false;
      }
      currentAnimation.detacher();
      currentAnimation.resolve();
      element.style.transition = element.style[currentAnimation.styleProp] = "";
      me.currentAnimation = null;
      me.trigger(`${currentAnimation.showing ? "show" : "hide"}AnimationEnd`);
    }
  }
  afterHideShowAnimate(currentAnimation) {
    const me = this;
    if (currentAnimation === me.currentAnimation) {
      me.element.classList.remove("b-hiding");
      me.cancelHideShowAnimation();
      if (me._hidden) {
        me.afterHideAnimation();
      }
    }
  }
  /**
   * Temporarily changes the {@link #property-isVisible} to yield `false` regardless of this
   * Widget's true visibility state. This can be useful for suspending operations which rely on
   * the {@link #property-isVisible} property.
   *
   * This increments a counter which {@link #function-resumeVisibility} decrements.
   * @internal
   */
  suspendVisibility() {
    this._visibilitySuspended = (this._visibilitySuspended || 0) + 1;
    Object.defineProperty(this, "isVisible", returnFalseProp);
  }
  /**
   * Resumes visibility. If the suspension counter is returned to zero by this, then the
   * {@link #event-paint} event is triggered, causing a cascade of `paint` events on all
   * descendants. This can be prevented by passing `false` as the only parameter.
   * @param {Boolean} [triggerPaint=true] Trigger the {@link #event-paint} event.
   * @internal
   */
  resumeVisibility(triggerPaint = true) {
    if (!--this._visibilitySuspended) {
      delete this.isVisible;
      if (triggerPaint) {
        this.triggerPaint();
      }
    }
  }
  /**
   * Hide widget
   * @param {Boolean} animate Pass `true` (default) to animate the hide action
   * @category Visibility
   * @returns {Promise} A promise which is resolved when the widget has been hidden
   */
  hide(animate = true) {
    return new Promise((resolve) => {
      const me = this;
      if (!me._hidden && me.trigger("beforeHide", { animate }) !== false) {
        const {
          element,
          lastAlignSpec
        } = me, { style } = element, hideAnimation = animate && me.hideAnimation;
        me.clearTimeout("scrollListenerTimeout");
        me.clearTimeout("resizeListenerTimeout");
        me._hidden = true;
        if (lastAlignSpec) {
          lastAlignSpec.targetOutOfView = null;
          if (lastAlignSpec.monitorIntersection) {
            me.intersectionObserver.takeRecords();
            me.intersectionObserver.unobserve(lastAlignSpec.target);
          }
        }
        me.onConfigChange({
          name: "hidden",
          value: true,
          was: false,
          config: me.$meta.configs.hidden
        });
        if (!element) {
          resolve();
          return;
        }
        if (element.matches(":focus-within")) {
          me.revertFocus(true);
        }
        if (me.isDestroyed) {
          resolve();
          return;
        }
        me.cancelHideShowAnimation();
        if (hideAnimation) {
          const styleProp = Object.keys(hideAnimation)[0], animProps = hideAnimation[styleProp];
          if (Number(getComputedStyle(me.element)[styleProp]) !== animProps.to) {
            const currentAnimation = me.currentAnimation = {
              hiding: true,
              styleProp,
              resolve
            };
            element.classList.add("b-hiding");
            me.isAnimating = true;
            currentAnimation.detacher = EventHelper.onTransitionEnd({
              element,
              property: styleProp,
              duration: parseDuration(animProps.duration) + 20,
              handler: () => {
                if (currentAnimation === me.currentAnimation) {
                  me.afterHideShowAnimate(currentAnimation);
                } else {
                  me.isAnimating = false;
                }
              },
              thisObj: me
            });
            style[styleProp] = animProps.from;
            DomHelper.getStyleValue(element, styleProp);
            style.transition = `${styleProp} ${animProps.duration} ease ${animProps.delay}`;
            style[styleProp] = animProps.to;
          } else {
            me.syncHiddenCls();
          }
        } else {
          me.syncHiddenCls();
        }
        me.ownedWidgets && Object.values(me.ownedWidgets).forEach((w) => {
          var _a5;
          return w.floating && ((_a5 = w.hide) == null ? void 0 : _a5.call(w, animate));
        });
        me.afterHide(!hideAnimation && resolve, hideAnimation);
      } else {
        resolve(false);
      }
    });
  }
  realignOnTransitionEnd(event) {
    this.doHideOrRealign(event);
  }
  doHideOrRealign({ target, type, isTrusted }) {
    const me = this, {
      lastAlignSpec,
      element
    } = me, anchoredTo = lastAlignSpec == null ? void 0 : lastAlignSpec.anchoredTo, lastTarget = lastAlignSpec == null ? void 0 : lastAlignSpec.target, position = lastAlignSpec == null ? void 0 : lastAlignSpec.position, activeEl = DomHelper.getActiveElement(me);
    if (
      // If it's a synthesized scroll event (such as from our ResizeMonitor polyfill), ignore it.
      !isTrusted || // Realign happens on frame, might have been removed from DOM so check whether it has layout
      !hasLayout(element) || // event.target might be missing with LockerService enabled. we still need to call the logic as it does not
      // depend much on the scroll target
      target && // If a scrolled element is not visible, ignore it.
      (type === "scroll" && !DomHelper.isVisible(target) || // If the scroll is inside our element, ignore it.
      element.contains(target) || target.nodeType === Element.ELEMENT_NODE && me.owns(target) || // If we're scrolling because a focused textual input field which we contain is being shifted into view,
      // we must not reposition - we'll just move with the document content.
      target.nodeType === Element.DOCUMENT_NODE && element.contains(activeEl) && DomHelper.isTextualInput(activeEl) && globalThis.innerHeight < document.body.offsetHeight)
    ) {
      return;
    }
    if (lastAlignSpec.aligningToElement) {
      const newTarget = DomHelper.isInView(lastTarget, false, me);
      if (newTarget && newTarget.equals(lastAlignSpec.targetRect)) {
        return;
      }
    }
    const xy2 = me.scrollAction === "hide" && me.getXY();
    me.scrollRealigning = type === "scroll";
    me.realign();
    me.scrollRealigning = false;
    if (!me.isDestroyed && isVisible(element) && me.scrollAction === "hide") {
      const [newX, newY] = me.getXY(), moved = newX !== xy2[0] || newY !== xy2[1];
      if ((lastAlignSpec == null ? void 0 : lastAlignSpec.aligningToEvent) || (moved || (lastTarget == null ? void 0 : lastTarget.$$name) === "Point" || position) && (!anchoredTo || !hasLayout(anchoredTo) || target && DomHelper.isDescendant(target, anchoredTo))) {
        me.hide();
      }
    }
  }
  afterHide(resolveFn = null, hideAnimation = this.hideAnimation) {
    var _a5, _b2;
    const me = this;
    me._anchor = me._configuredAnchorState;
    me.removeTransientListeners();
    if (!hideAnimation) {
      me.afterHideAnimation();
    }
    me.trigger("hide");
    (_b2 = (_a5 = me.owner) == null ? void 0 : _a5.onChildHide) == null ? void 0 : _b2.call(_a5, me);
    resolveFn && resolveFn();
  }
  removeTransientListeners() {
    var _a5, _b2;
    const me = this, { targetObserver } = me;
    me.clearTimeout("resizeListenerTimeout");
    me.clearTimeout("scrollListenerTimeout");
    if (targetObserver) {
      targetObserver.disconnect();
      delete me.targetObserver;
    }
    me.documentScrollListener = (_a5 = me.documentScrollListener) == null ? void 0 : _a5.call(me);
    me.targetRootScrollListener = (_b2 = me.targetRootScrollListener) == null ? void 0 : _b2.call(me);
    if (me.observedTargetEl) {
      ResizeMonitor.removeResizeListener(me.observedTargetEl, me.onTargetResize);
      me.observedTargetEl = false;
    }
    if (me.observedConstrainEl) {
      ResizeMonitor.removeResizeListener(me.observedConstrainEl, me.onAlignConstraintChange);
      me.observedConstrainEl = false;
    }
  }
  afterHideAnimation() {
    const me = this, { element } = me;
    if (!me.hasOwnProperty("_hideMode") && me.floating && me.floatRoot.contains(element)) {
      element.remove();
    } else {
      me.syncHiddenCls();
    }
    if (me.defaultAnchorBackgroundColor) {
      me.anchorPathElement.setAttribute("fill", me.defaultAnchorBackgroundColor);
    }
  }
  changeHidden(value2) {
    const me = this;
    let ret;
    if (me.isConfiguring) {
      ret = Boolean(value2);
      me.syncHiddenCls(ret);
    } else {
      me.trigger("beforeChangeHidden", { hidden: value2 });
      if (value2) {
        me.hide();
      } else {
        me.show();
      }
    }
    return ret;
  }
  syncHiddenCls(_hidden = this._hidden) {
    const { element, hideMode } = this, classes = {
      "b-hidden": _hidden
    };
    for (const c of hideModes) {
      classes[`b-hide-mode-${c}`] = c === hideMode && (_hidden || c !== "display");
    }
    DomHelper.assignClasses(element, classes);
  }
  /**
   * Get id assigned by user (not generated id)
   * @returns {String}
   * @readonly
   * @private
   * @category Misc
   */
  get assignedId() {
    return this.hasGeneratedId ? null : this.id;
  }
  /**
   * Get this Widget's parent when used as a child in a {@link Core.widget.Container},
   * @member {Core.widget.Widget} parent
   * @getter
   * @setter
   * @category Widget hierarchy
   */
  /**
   * The owning Widget of this Widget. If this Widget is directly contained (i.e. it is one
   * of the {@link Core.widget.Container#property-items} of a Container), __this property will
   * be read-only, and will refer to the containing Widget__.
   *
   * If this property has not been set and this is a `Popup` with a {@link Core.widget.Popup#config-forElement},
   * this config will refer to that element's encapsulating Widget.
   *
   * If this Widget is floating, this property should be set by the developer.
   *
   * Registering with an `owner` creates a lifecycle relationship with that owning Widget. When
   * the `owner` is destroyed this widget will also be destroyed.
   *
   * A widget can unregister from its `owner` and break this relationship at any time by setting
   * the property to `null`. It is then the developer's responsibility to ensure that this Widget
   * is destroyed.
   * @member {Core.widget.Widget|Grid.column.Column}
   * @typings {ContainerItem|GridColumn}
   * @readonly
   * @category Widget hierarchy
   */
  get owner() {
    return this.parent || this._owner || this.containingWidget;
  }
  changeOwner(owner) {
    if (!this.parent) {
      return owner;
    }
  }
  updateOwner(owner, previousOwner) {
    const me = this, { id: id2 } = me;
    if (previousOwner) {
      previousOwner.ownedWidgets && delete previousOwner.ownedWidgets[id2];
    }
    if (owner && owner !== me.parent) {
      (owner.ownedWidgets || (owner.ownedWidgets = {}))[id2] = me;
    }
  }
  get containingWidget() {
    var _a5, _b2;
    let result = this.parent;
    if (!result) {
      const owningEl = ((_a5 = this.forElement) == null ? void 0 : _a5.nodeType) === Element.ELEMENT_NODE ? this.forElement : (_b2 = this.element) == null ? void 0 : _b2.parentNode;
      result = (owningEl == null ? void 0 : owningEl.closest(".b-widget")) && _Widget.fromElement(owningEl);
    }
    return result;
  }
  /**
   * Get this Widget's previous sibling in the parent {@link Core.widget.Container Container}, or, if not
   * in a Container, the previous sibling widget in the same _parentElement_.
   * @property {Core.widget.Widget}
   * @readonly
   * @category Widget hierarchy
   */
  get previousSibling() {
    return this.getSibling(-1);
  }
  /**
   * Get this Widget's next sibling in the parent {@link Core.widget.Container Container}, or, if not
   * in a Container, the next sibling widget in the same _parentElement_.
   * @property {Core.widget.Widget}
   * @readonly
   * @category Widget hierarchy
   */
  get nextSibling() {
    return this.getSibling(1);
  }
  getSibling(increment) {
    var _a5, _b2;
    const me = this, { parent } = me, siblings = parent ? parent.childItems : Array.from((_b2 = (_a5 = me.element.parentElement) == null ? void 0 : _a5.querySelectorAll(".b-widget")) != null ? _b2 : []);
    return parent ? siblings[siblings.indexOf(me) + increment] : _Widget.fromElement(siblings[siblings.indexOf(me.element) + increment]);
  }
  /**
   * Looks up the {@link #property-owner} axis to find an ancestor which matches the passed selector.
   * The selector may be a widget type identifier, such as `'grid'`, or a function which will return
   * `true` when passed the desired ancestor.
   * @param {String|Function} [selector] A Type identifier or selection function. If not provided, this method returns
   * the {@link #property-owner} of this widget
   * @param {Boolean} [deep] When using a string identifier, pass `true` if all superclasses should be included, i.e.,
   * if a `Grid` should match `'widget'`.
   * @param {Number|String|Core.widget.Widget} [limit] how many steps to step up before aborting the search, or a
   * selector to stop at or the topmost ancestor to consider.
   * @returns {Core.widget.Widget|*}
   * @category Widget hierarchy
   */
  up(selector, deep, limit) {
    var _a5;
    const { owner } = this;
    return selector ? (_a5 = owner == null ? void 0 : owner.closest) == null ? void 0 : _a5.call(owner, selector, deep, limit) : owner;
  }
  /**
   * Starts with this Widget, then Looks up the {@link #property-owner} axis to find an ancestor which matches the
   * passed selector. The selector may be a widget type identifier, such as `'grid'`, or a function which will return
   * `true` when passed the desired ancestor.
   * @param {String|Function} selector A Type identifier or selection function.
   * @param {Boolean} [deep] When using a string identifier, pass `true` if all superclasses should be included, i.e.,
   * if a `Grid` should match `'widget'`.
   * @param {Number|String|Core.widget.Widget} [limit] how many steps to step up before aborting the search, or a
   * selector to stop at or the topmost ancestor to consider.
   * @category Widget hierarchy
   */
  closest(selector, deep, limit) {
    const limitType = typeof limit, numericLimit = limitType === "number", selectorLimit = limitType === "string";
    for (let result = this, steps = 1; result; result = result.owner, steps++) {
      if (_Widget.widgetMatches(result, selector, deep)) {
        return result;
      }
      if (numericLimit && steps >= limit) {
        return;
      } else if (selectorLimit && _Widget.widgetMatches(result, limit, deep)) {
        return;
      } else if (result === limit) {
        return;
      }
    }
  }
  /**
   * Returns `true` if this Widget owns the passed Element, Event or Widget. This is based on the widget hierarchy,
   * not DOM containment. So an element in a `Combo`'s dropdown list will be owned by the `Combo`.
   * @param {HTMLElement|Event|Core.widget.Widget} target The element event or Widget to test for being
   * within the ownership tree of this Widget.
   * @category Widget hierarchy
   */
  owns(target, includeOwnedWidgets) {
    if (target) {
      if ("eventPhase" in target) {
        target = target.target;
      }
      if (target.nodeType === Element.ELEMENT_NODE) {
        if (this.element.contains(target)) {
          return true;
        }
        target = target.getAttribute("owned-by") ? _Widget.getById(target.getAttribute("owned-by")) : _Widget.fromElement(target);
      }
      while (target) {
        if (target.isHint || target === this || includeOwnedWidgets && this.ownedWidgets && Object.values(this.ownedWidgets).some((ownedWidget) => ownedWidget.owns(target, true))) {
          return true;
        }
        target = target.owner;
      }
    }
    return false;
  }
  /**
   * Iterate over all ancestors of this widget.
   *
   * *Note*: Due to this method aborting when the function returns `false`, beware of using short form arrow
   * functions. If the expression executed evaluates to `false`, iteration will terminate.
   * @param {Function} fn Function to execute for all ancestors. Terminate iteration by returning `false`.
   * @returns {Boolean} Returns `true` if iteration was not aborted by a step returning `false`
   * @category Widget hierarchy
   */
  eachAncestor(fn2) {
    let ancestor = this.owner;
    while (ancestor) {
      if (fn2(ancestor) === false) {
        return false;
      }
      ancestor = ancestor.owner;
    }
    return true;
  }
  changeMaximizeOnMobile(maximizeOnMobile) {
    const me = this;
    if (me.floating && BrowserHelper.isMobile) {
      const { initialConfig } = me;
      if (maximizeOnMobile) {
        me.centered = me.modal = false;
        me.maximized = true;
      } else {
        me.centered = initialConfig.centered;
        me.modal = initialConfig.modal;
        me.maximized = initialConfig.maximized;
      }
    }
  }
  changeMonitorResize(monitorResize, oldMonitorResize) {
    const result = this.scaleToFitWidth ? false : Boolean(monitorResize);
    if (result !== Boolean(oldMonitorResize)) {
      return result;
    }
  }
  updateMonitorResize(monitorResize) {
    const me = this;
    if (!hasOwn6(me, "onElementResize")) {
      me.onElementResize = me.onElementResize.bind(me);
    }
    ResizeMonitor[monitorResize ? "addResizeListener" : "removeResizeListener"](me.element, me.onElementResize);
  }
  changeReadOnly(readOnly) {
    readOnly = Boolean(readOnly);
    if (Boolean(this._readOnly) !== readOnly) {
      return readOnly;
    }
  }
  updateReadOnly(readOnly) {
    var _a5;
    (_a5 = this.element) == null ? void 0 : _a5.classList[readOnly ? "add" : "remove"]("b-readonly");
    if (!this.isConfiguring) {
      this.eachWidget((widget) => {
        if (widget.ignoreParentReadOnly) {
          return;
        }
        if (!("_originalReadOnly" in widget)) {
          widget._originalReadOnly = widget.config.readOnly || false;
        }
        widget.readOnly = readOnly || widget._originalReadOnly;
      }, false);
      this.trigger("readOnly", { readOnly });
    }
  }
  /**
   * Iterate over all widgets owned by this widget and any descendants.
   *
   * *Note*: Due to this method aborting when the function returns `false`, beware of using short form arrow
   * functions. If the expression executed evaluates to `false`, iteration will terminate.
   * @param {Function} fn A function to execute upon each descendant widget.
   * Iteration terminates if this function returns `false`.
   * @param {Core.widget.Widget} fn.widget The current descendant widget.
   * @param {Object} fn.control An object containing recursion control options.
   * @param {Boolean} fn.control.down A copy of the `deep` parameter. This can be adjusted by `fn` to decide which
   * widgets should be recursed. This value will always be the value of `deep` on entry and the value of `control.down`
   * upon return determines the recursion into the current widget.
   * @param {Boolean} [deep=true] Pass as `false` to only consider immediate child widgets.
   * @returns {Boolean} Returns `true` if iteration was not aborted by a step returning `false`
   * @category Widget hierarchy
   */
  eachWidget(fn2, deep = true, itemsBeforeAux) {
    const widgets = itemsBeforeAux ? this.childItemsOrderedByRelevance : this.childItems, length = (widgets == null ? void 0 : widgets.length) || 0, control = {};
    for (let i = 0; i < length; i++) {
      const widget = widgets[i];
      control.down = deep;
      if (fn2(widget, control) === false) {
        return false;
      }
      if (control.down && widget.eachWidget) {
        if (widget.eachWidget(fn2, deep, itemsBeforeAux) === false) {
          return false;
        }
      }
    }
    return true;
  }
  /**
   * Returns an array of all descendant widgets which the passed
   * filter function returns `true` for.
   * @param {Function} filter A function which, when passed a widget,
   * returns `true` to include the widget in the results.
   * @returns {Core.widget.Widget[]} All matching descendant widgets.
   * @category Widget hierarchy
   */
  queryAll(filter2) {
    const result = [];
    this.eachWidget((w) => {
      if (filter2(w)) {
        result.push(w);
      }
    });
    return result;
  }
  /**
   * Returns the first descendant widgets which the passed
   * filter function returns `true` for.
   * @param {Function} filter A function which, when passed a widget,
   * returns `true` to return the widget as the sole result.
   * @returns {Core.widget.Widget} The first matching descendant widget.
   * @category Widget hierarchy
   */
  query(filter2, itemsBeforeAux) {
    let result = null;
    this.eachWidget((w) => {
      if (filter2(w)) {
        result = w;
        return false;
      }
    }, true, itemsBeforeAux);
    return result;
  }
  /**
   * Get a widget by ref, starts on self and traverses up the owner hierarchy checking `widgetMap` at each level.
   * Not checking the top level widgetMap right away to have some acceptance for duplicate refs.
   * @param {String} ref ref to find
   * @returns {Core.widget.Widget}
   * @internal
   * @category Widget hierarchy
   */
  getWidgetByRef(ref) {
    var _a5, _b2;
    if (ref instanceof _Widget) {
      return ref;
    }
    return ((_a5 = this == null ? void 0 : this.widgetMap) == null ? void 0 : _a5[ref]) || ((_b2 = this == null ? void 0 : this.owner) == null ? void 0 : _b2.getWidgetByRef(ref));
  }
  onFocusIn(e) {
    const me = this, { element } = me;
    me.containsFocus = true;
    me.focusInEvent = e;
    if (me.floating || me.positioned) {
      me.toFront();
    }
    element.classList.add("b-contains-focus");
    me.updateAriaLabel(me.localizeProperty("ariaLabel"));
    me.updateAriaDescription(me.localizeProperty("ariaDescription"));
    if (element.contains(e._target) && me.onInternalKeyDown && !me.keyDownListenerRemover) {
      me.keyDownListenerRemover = EventHelper.on({
        element,
        keydown: "onInternalKeyDown",
        thisObj: me
      });
    }
    me.trigger("focusin", e);
  }
  onFocusOut(e) {
    const me = this;
    if (me.keyDownListenerRemover) {
      me.keyDownListenerRemover();
      me.keyDownListenerRemover = null;
    }
    if (!me.isDestroyed) {
      if (!e.relatedTarget) {
        me.revertFocus(!me.isVisible);
      }
      me.containsFocus = false;
      me.element.classList.remove("b-contains-focus");
      me.updateAriaLabel(me.localizeProperty("ariaLabel"));
      me.updateAriaDescription(me.localizeProperty("ariaDescription"));
      me.trigger("focusout", e);
    }
  }
  /**
   * Returns a function that will set the focus (`document.activeElement`) to the most consistent element possible
   * based on the focus state at the time this method was called. Derived classes can implement `captureFocusItem()`
   * to refine this process to include logical items (e.g., a grid cell) that would be more stable than DOM element
   * references.
   *
   * If this widget does not contain the focus, the returned function will do nothing.
   * @returns {Function}
   * @internal
   */
  captureFocus() {
    const me = this, activeElementWas = DomHelper.getActiveElement(me), restore = me.contains(activeElementWas) && me.captureFocusItem(activeElementWas);
    return (scrollIntoView, force) => {
      if (restore && !me.isDestroying) {
        const activeElementNow = DomHelper.getActiveElement(me);
        if (activeElementNow !== activeElementWas || force) {
          restore(scrollIntoView);
        }
      }
    };
  }
  /**
   * This method is called by `captureFocus()` when this widget contains the focus and it returns a function that
   * restores the focus to the correct internal element. The returned function is only called if the current
   * `document.activeElement` is different from the passed `activeElement`.
   *
   * This method can be replaced by derived classes to capture stable identifiers for the currently focused, logical
   * item (for example, a cell of a grid).
   *
   * @param {HTMLElement} activeElement The current `document.activeElement`.
   * @returns {Function} Returns a function that accepts a boolean argument. Defaults to `true`, `false` attempts to
   * focus without scrolling.
   * @internal
   */
  captureFocusItem(activeElement) {
    return (scrollIntoView = true) => {
      if (this.contains(activeElement)) {
        scrollIntoView ? activeElement.focus() : activeElement.focus({ preventScroll: true });
      }
    };
  }
  /**
   * Returns `true` if this widget is or contains the specified element or widget.
   * @param {HTMLElement|Core.widget.Widget} elementOrWidget The element or widget
   * @param {Boolean} [strict] Pass `true` to test for strict containment (if `elementOrWidget` is this widget, the
   * return value will be `false`).
   * @returns {Boolean}
   * @category Widget hierarchy
   */
  contains(elementOrWidget, strict) {
    const { element } = this;
    if (elementOrWidget && element) {
      if (elementOrWidget.isWidget) {
        elementOrWidget = elementOrWidget.element;
      }
      return element.contains(elementOrWidget) && (!strict || element !== elementOrWidget);
    }
  }
  /**
   * If this Widget contains focus, focus is reverted to the source from which it entered if possible,
   * or to a close relative if not.
   * @param {Boolean} [force] Pass as `true` to move focus to the previously focused item, or the
   * closest possible relative even if this widget does not contain focus.
   * @advanced
   */
  revertFocus(force) {
    var _a5;
    const me = this, activeElement = DomHelper.getActiveElement(me);
    let target = (_a5 = me.focusInEvent) == null ? void 0 : _a5.relatedTarget;
    if (force || me.containsFocus && (target == null ? void 0 : target.nodeType) === Element.ELEMENT_NODE && me.element.contains(activeElement)) {
      if (!target || !DomHelper.isFocusable(target)) {
        target = me.getFocusRevertTarget();
      }
      me._isRevertingFocus = true;
      if (target && DomHelper.isFocusable(target)) {
        target._isRevertingFocus = true;
        target.focus({ preventScroll: true });
        target._isRevertingFocus = false;
      } else {
        activeElement == null ? void 0 : activeElement.blur();
      }
      me._isRevertingFocus = false;
    }
  }
  /**
   * This method finds a close sibling (or parent, or parent's sibling etc. recursively) to which focus
   * can be directed in the case of revertFocus not having a focusable element from our focusInEvent.
   *
   * This can happen when the "from" component is destroyed or hidden. We should endeavour to prevent
   * focus escaping to `document.body` for accessibility and ease of use, and keep focus close.
   * @internal
   */
  getFocusRevertTarget() {
    var _a5;
    const me = this, {
      owner,
      focusInEvent
    } = me, searchDirection = focusInEvent ? focusInEvent.backwards ? 1 : -1 : -1;
    let target = focusInEvent && focusInEvent.relatedTarget;
    const toComponent = target && _Widget.fromElement(target);
    if (owner && !owner.isDestroyed && (!target || !DomHelper.isFocusable(target) || toComponent && !toComponent.isFocusable)) {
      target = null;
      if (owner.eachWidget) {
        const siblings = [];
        owner.eachWidget((w) => {
          if (w === me || w.isFocusable) {
            siblings.push(w);
          }
        }, false);
        if (siblings.length > 1) {
          const myIndex = siblings.indexOf(me);
          target = siblings[myIndex + searchDirection] || siblings[myIndex - searchDirection];
        }
      }
      if (!target && owner.isFocusable) {
        target = owner;
      }
      target = target ? target.focusElement : (_a5 = owner.getFocusRevertTarget) == null ? void 0 : _a5.call(owner);
    }
    return target;
  }
  /**
   * Returns a `DomClassList` computed from the `topMostBase` (e.g., `Widget` or `Panel`) with the given `suffix`
   * appended to each `widgetClass`.
   * @param {Function} topMostBase The top-most base class constructor at which to start gathering classes.
   * @param {String} [suffix] An optional suffix to apply to all widget classes.
   * @returns {Core.helper.util.DomClassList}
   * @internal
   * @category DOM
   */
  getStaticWidgetClasses(topMostBase, suffix) {
    const classList = new DomClassList(), hierarchy = this.$meta.hierarchy;
    let cls, i, name, widgetClass, widgetClassProperty;
    for (i = hierarchy.indexOf(topMostBase); i < hierarchy.length; ++i) {
      cls = hierarchy[i];
      widgetClassProperty = Reflect.getOwnPropertyDescriptor(cls.prototype, "widgetClass");
      if (widgetClassProperty == null ? void 0 : widgetClassProperty.get) {
        widgetClass = widgetClassProperty.get.call(this);
      } else {
        name = hasOwn6(cls, "$$name") || hasOwn6(cls, "$name") || hasOwn6(cls, "_$name") ? cls.$$name : cls.name;
        if (name.length < 3 || name.includes("$")) {
          console.warn(
            `Class "${name}" extending "${cls.$name}" should have "$name" static getter with no less than 3 chars.`
          );
        }
        widgetClass = `b-${name.toLowerCase()}`;
      }
      if (widgetClass) {
        classList.add(suffix ? widgetClass + suffix : widgetClass);
      }
    }
    return classList;
  }
  get rootUiClass() {
    return _Widget;
  }
  /**
   * Returns the `DomClassList` for this widget's class. This object should not be mutated.
   * @returns {Core.helper.util.DomClassList}
   * @internal
   * @category DOM
   */
  get staticClassList() {
    const { $meta: meta } = this;
    let classList = meta.staticClassList;
    if (!classList) {
      meta.staticClassList = classList = this.getStaticWidgetClasses(_Widget);
      BrowserHelper.isTouchDevice && classList.add("b-touch");
    }
    return classList;
  }
  /**
   * Returns the cross-product of the classes `staticClassList` with each `ui` as an array of strings.
   *
   * For example, a Combo with a `ui: 'foo bar'` would produce:
   *
   *      [
   *          'b-widget-foo', 'b-field-foo', 'b-textfield-foo', 'b-pickerfield-foo', 'b-combo-foo',
   *          'b-widget-bar', 'b-field-bar', 'b-textfield-bar', 'b-pickerfield-bar', 'b-combo-bar'
   *      ]
   *
   * @returns {String[]}
   * @internal
   * @category DOM
   */
  get uiClasses() {
    this.getConfig("ui");
    return this._uiClasses;
  }
  /**
   * Returns the cross-product of the classes `staticClassList` with each `ui` as a `DomClassList` instance.
   *
   * For example, a Combo with a `ui: 'foo bar'` would produce:
   *
   * ```javascript
   *      new DomClassList({
   *          'b-field-ui-foo'       : 1,
   *          'b-textfield-ui-foo'   : 1,
   *          'b-pickerfield-ui-foo' : 1,
   *          'b-combo-ui-foo'       : 1,
   *
   *          'b-field-ui-bar'       : 1,
   *          'b-textfield-ui-bar'   : 1,
   *          'b-pickerfield-ui-bar' : 1,
   *          'b-combo-ui-bar'       : 1
   *      });
   * ```
   *
   * A Panel with a `ui: 'foo bar'` would produce:
   *
   * ```javascript
   *      new DomClassList({
   *          'b-panel-ui-foo' : 1,
   *          'b-panel-ui-bar' : 1
   *      });
   * ```
   * @returns {Core.helper.util.DomClassList}
   * @internal
   * @category DOM
   */
  get uiClassList() {
    this.getConfig("ui");
    return this._uiClassList;
  }
  /**
   * Used by the Widget class internally to create CSS classes based on this Widget's
   * inheritance chain to allow styling from each level to apply.
   *
   * For example Combo would yield `"["b-widget", "b-field", "b-textfield", "b-pickerfield", "b-combo"]"`
   *
   * May be implemented in subclasses to add or remove classes from the super.widgetClassList
   * @returns {String[]} The css class list named using the class name.
   * @internal
   * @category DOM
   */
  get widgetClassList() {
    const me = this, { cls, defaultCls, uiClasses } = me;
    let { staticClassList } = me;
    if (defaultCls || cls) {
      staticClassList = staticClassList.clone();
      defaultCls && staticClassList.assign(defaultCls);
      cls && staticClassList.assign(cls);
    }
    const classList = staticClassList.values;
    uiClasses && classList.push(...uiClasses);
    me.floating && classList.push("b-floating");
    if (me.collapsify === "hide") {
      classList.push("b-collapsify-hide");
    }
    return classList;
  }
  changeCls(cls) {
    return DomClassList.from(cls);
  }
  updateCls(cls, was) {
    if (!this.isConfiguring && !this.isComposable) {
      const { element } = this;
      if (was) {
        ObjectHelper.getTruthyKeys(was).forEach((c) => element.classList.remove(c));
      }
      cls.assignTo(element);
    }
  }
  changeContentElementCls(cls) {
    return DomClassList.from(cls);
  }
  changeHtmlCls(cls) {
    return DomClassList.from(cls);
  }
  changeDefaultCls(cls) {
    return DomClassList.from(
      cls,
      /* returnEmpty */
      true
    );
  }
  updateHideMode() {
    this.hasPainted && this.syncHiddenCls();
  }
  changeUi(ui) {
    return DomClassList.from(ui);
  }
  updateUi(ui) {
    let uiClassList = null, cls, suffix;
    if (ui) {
      const staticClassList = this.getStaticWidgetClasses(this.rootUiClass);
      for (suffix in ui) {
        if (ui[suffix]) {
          for (cls in staticClassList) {
            if (staticClassList[cls]) {
              (uiClassList || (uiClassList = new DomClassList()))[`${cls}-ui-${suffix}`] = 1;
            }
          }
        }
      }
    }
    this._uiClasses = uiClassList == null ? void 0 : uiClassList.values;
    this._uiClassList = uiClassList;
  }
  //endregion
  //region Cache
  /**
   * Gets dom elements in the view. Caches the results for faster future calls.
   * @param {String} query CSS selector
   * @param {Boolean} children true to fetch multiple elements
   * @param {HTMLElement} element Element to use as root for the query, defaults to the views outermost element
   * @returns {HTMLElement|HTMLElement[]|null} A single element or an array of elements (if parameter children is set to `true`)
   * @internal
   * @category DOM
   */
  fromCache(query, children = false, element = this.element) {
    var _a5;
    if (!element) {
      return null;
    }
    const me = this;
    if (!((_a5 = me.cache) == null ? void 0 : _a5[query])) {
      if (!me.cache) {
        me.cache = {};
      }
      return me.cache[query] = children ? DomHelper.children(element, query) : element.querySelector(query);
    }
    return me.cache[query];
  }
  /**
   * Clear caches, forces all calls to fromCache to requery dom. Called on render/rerender.
   * @internal
   * @category DOM
   */
  emptyCache() {
    if (this.cache) {
      this.cache = {};
    }
  }
  //endregion
  //region Mask
  changeMasked(mask, maskInstance) {
    var _a5;
    if (((_a5 = this.masked) == null ? void 0 : _a5.type) === "trial") {
      return;
    }
    if (mask === true || mask === "") {
      mask = "\xA0";
    }
    if (maskInstance && !maskInstance.isDestroyed) {
      if (typeof mask === "string") {
        maskInstance.text = mask;
        mask = maskInstance;
      } else if (mask) {
        maskInstance.setConfig(mask);
        mask = maskInstance;
      } else {
        maskInstance.destroy();
      }
    } else if (mask) {
      const Mask2 = _Widget.resolveType("mask");
      mask = Mask2.mergeConfigs(this.maskDefaults, mask);
      mask.owner = this;
      mask = Mask2.mask(mask);
    }
    return mask || null;
  }
  onMaskAutoClose(mask) {
    if (mask.isDestroyed && mask === this.masked) {
      this.masked = null;
    }
  }
  /**
   * Mask the widget, showing the specified message
   * @param {String|MaskConfig} msg Mask message (or a {@link Core.widget.Mask} config object
   * @returns {Core.widget.Mask}
   */
  mask(msg) {
    this.masked = msg;
    return this.masked;
  }
  /**
   * Unmask the widget
   */
  unmask() {
    this.masked = null;
  }
  //endregion
  //region Monitor resize
  onInternalResize(element, width, height, oldWidth, oldHeight) {
    this._width = element.offsetWidth;
    this._height = element.offsetHeight;
  }
  onElementResize(resizedElement, lastRect) {
    var _a5;
    const me = this, { element } = me, oldWidth = (_a5 = me._width) != null ? _a5 : me._oldWidth, oldHeight = me._height, newWidth = element.offsetWidth, newHeight = element.offsetHeight;
    if (me.floating && lastRect) {
      me.onFloatingWidgetResize(...arguments);
    }
    if (!me.suspendResizeMonitor && (oldWidth !== newWidth || oldHeight !== newHeight)) {
      me.onInternalResize(element, newWidth, newHeight, oldWidth, oldHeight);
      me.trigger("resize", { width: newWidth, height: newHeight, oldWidth, oldHeight });
    }
  }
  onFloatingWidgetResize(resizedElement, lastRect, myRect) {
    const me = this, {
      lastAlignSpec,
      constrainTo
    } = me;
    if (me.isVisible && lastAlignSpec && lastAlignSpec.target) {
      const heightChange = !lastRect || myRect.height !== lastRect.height, widthChange = !lastRect || myRect.width !== lastRect.width, failsConstraint = constrainTo && !Rectangle.from(constrainTo).contains(Rectangle.from(me.element, null, true));
      if (heightChange && lastAlignSpec.zone !== 2 || widthChange && lastAlignSpec.zone !== 1 || failsConstraint) {
        me.requestAnimationFrame(() => me.realign());
      }
    }
  }
  updateScale() {
    const me = this, element = me.element, parentElement = element.parentElement;
    if (!me.configuredWidth) {
      me.configuredWidth = me.width;
    }
    element.style.display = "none";
    const rect = Rectangle.client(parentElement), scale = rect.width / me.configuredWidth, adjustedScale = me.scale = me.allowGrowWidth ? Math.min(scale, 1) : scale;
    element.style.transform = `scale(${adjustedScale})`;
    element.style.transformOrigin = "top left";
    element.style.display = "";
    if (me.allowGrowWidth && scale > 1) {
      me.width = me.configuredWidth * scale;
    }
  }
  onParentElementResize(event) {
    this.updateScale();
  }
  //endregion
  /**
   * Returns a `TRBL` array of values parse from the passed specification. This can be used to parse`
   * a value list for `margin` or `padding` or `border-width` etc - any CSS value which takes a `TRBL` value.
   * @param {Number|String|String[]} values The `TRBL` value
   * @param {String} [units=px] The units to add to values which are specified as numeric.
   * @internal
   */
  parseTRBL(values, units = "px") {
    values = values || 0;
    if (typeof values === "number") {
      return [`${values}${units}`, `${values}${units}`, `${values}${units}`, `${values}${units}`];
    }
    const parts = values.split(" "), len = parts.length;
    if (len === 1) {
      parts[1] = parts[2] = parts[3] = parts[0];
    } else if (len === 2) {
      parts[2] = parts[0];
      parts[3] = parts[1];
    } else if (len === 3) {
      parts[3] = parts[1];
    }
    return [
      isFinite(parts[0]) ? `${parts[0]}${units}` : parts[0],
      isFinite(parts[1]) ? `${parts[1]}${units}` : parts[2],
      isFinite(parts[2]) ? `${parts[2]}${units}` : parts[3],
      isFinite(parts[3]) ? `${parts[3]}${units}` : parts[4]
    ];
  }
  // Returns root node for this widget, either a document or a shadowRoot
  get documentRoot() {
    var _a5;
    return ((_a5 = this.owner) == null ? void 0 : _a5.documentRoot) || this.element.getRootNode();
  }
  // Returns the root from which to add global events. Prioritizes owner last.
  get eventRoot() {
    var _a5, _b2;
    return ((_a5 = this.element) == null ? void 0 : _a5.isConnected) ? DomHelper.getRootElement(this.element) : ((_b2 = this.owner) == null ? void 0 : _b2.eventRoot) || this._rootElement;
  }
  // Returns top most DOM element of the visible DOM tree for this widget element, either document.body or a shadowRoot
  get rootElement() {
    var _a5, _b2;
    const me = this;
    if (!me._rootElement) {
      let root = ((_a5 = me.owner) == null ? void 0 : _a5.rootElement) || DomHelper.getRootElement(me.forElement || (((_b2 = me.element) == null ? void 0 : _b2.isConnected) ? me.element : me.getRenderContext()[0] || me.element));
      if (!root) {
        root = document.body;
      }
      me._rootElement = root;
    }
    return me._rootElement;
  }
  get floatRoot() {
    var _a5;
    const me = this, { rootElement } = me;
    let { floatRoot } = rootElement;
    if (!floatRoot) {
      const { outerCls } = _Widget, themeName = (_a5 = DomHelper.getThemeInfo(null, rootElement)) == null ? void 0 : _a5.name;
      if (!DomHelper.isValidFloatRootParent(rootElement)) {
        throw new Error("Attaching float root to wrong root");
      }
      if (themeName) {
        outerCls.push(`b-theme-${themeName.toLowerCase()}`);
      }
      floatRoot = rootElement.floatRoot = DomHelper.createElement({
        className: `b-float-root ${outerCls.join(" ")}`,
        parent: rootElement
      });
      floatRoots.push(floatRoot);
      if (BrowserHelper.isAndroid) {
        floatRoot.style.height = "100dvh";
        EventHelper.on({
          element: globalThis,
          orientationchange: () => floatRoot.style.height = `${screen.height}px`,
          thisObj: this
        });
      }
      GlobalEvents_default.ion({
        theme: ({ theme, prev }) => {
          floatRoot.classList.add(`b-theme-${theme.toLowerCase()}`);
          floatRoot.classList.remove(`b-theme-${prev.toLowerCase()}`);
        }
      });
    } else if (!rootElement.contains(floatRoot)) {
      rootElement.appendChild(floatRoot);
    }
    floatRoot.classList.toggle("b-rtl", Boolean(me.rtl));
    return floatRoot;
  }
  get floatRootMaxZIndex() {
    let max3 = 1;
    Array.from(this.floatRoot.children).forEach((child) => {
      const zIndex = parseInt(getComputedStyle(child).zIndex || 0, 10);
      if (zIndex > max3) {
        max3 = zIndex;
      }
    });
    return max3;
  }
  /**
   * This property yields `true` if the currently focused element has been reached through other means
   * than mouse click. If the `activeElement` matches `:focus-visible`.
   * @property {Boolean}
   * @category Accessibility
   */
  get focusVisible() {
    return DomHelper.getActiveElement(this.element).matches(":focus-visible");
  }
  /**
   * This property yields `true` if the currently focused element has been reached through other means
   * than mouse click. If the `activeElement` matches `:focus-visible`.
   * @property {Boolean}
   * @readonly
   * @internal
   */
  static get focusVisible() {
    return DomHelper.getActiveElement().matches(":focus-visible");
  }
  static get floatRoots() {
    return floatRoots;
  }
  static removeFloatRoot(floatRoot) {
    floatRoots.splice(floatRoots.indexOf(floatRoot), 1);
  }
  // CSS classes describing outer-most Widgets to provide styling / behavioral CSS style rules
  static get outerCls() {
    const result = ["b-outer"], { platform } = BrowserHelper;
    if (platform) {
      result.push(`b-${platform}`);
    }
    if (BrowserHelper.isTouchDevice) {
      result.push("b-touch-events");
    }
    if (BrowserHelper.isMobile) {
      result.push("b-mobile");
    }
    if (DomHelper.scrollBarWidth) {
      result.push("b-visible-scrollbar");
    } else {
      result.push("b-overlay-scrollbar");
    }
    if (BrowserHelper.isChrome) {
      result.push("b-chrome");
    } else if (BrowserHelper.isSafari) {
      result.push("b-safari");
    } else if (BrowserHelper.isFirefox) {
      result.push("b-firefox");
    }
    if (BrowserHelper.isPhone) {
      result.push("b-phone");
    }
    if (!globalThis.ResizeObserver) {
      result.push("b-no-resizeobserver");
    }
    return result;
  }
  get isAnimating() {
    return this._isAnimatingCounter > 0;
  }
  set isAnimating(value2) {
    const me = this, { _isAnimatingCounter } = me;
    me._isAnimatingCounter = Math.max(0, _isAnimatingCounter + (value2 ? 1 : -1));
    if (_isAnimatingCounter === 0 && value2) {
      me.element.classList.add("b-animating");
      me.trigger("animationStart");
    } else if (_isAnimatingCounter === 1 && !value2) {
      me.element.classList.remove("b-animating");
      me.trigger("animationEnd");
    }
  }
  // Waits until all transitions are completed
  async waitForAnimations() {
    if (this.isAnimating) {
      await this.await("animationEnd", { checkLog: false });
    }
  }
  // Takes a snapshot of animations running before and after the function executes and waits for any animations
  // started due to that function to finish
  // Overridden for SalesForce in WidgetOverrideExecuteAndAwaitAnimations.js due to lack of support for
  // element.getAnimations()
  async executeAndAwaitAnimations(element, fn2, callback) {
    const animationsBeforeFunction = element.getAnimations({ subtree: true });
    await fn2();
    const animationsAfterFunction = element.getAnimations({ subtree: true }), animationsFromFunction = ArrayHelper.delta(animationsBeforeFunction, animationsAfterFunction).onlyInB, finiteAnimations = animationsFromFunction.filter((animation) => animation.effect.getTiming().iterations !== Infinity);
    if (finiteAnimations.length) {
      await Promise.allSettled(finiteAnimations.map((animation) => animation.finished));
    }
    if (!this.isDestroyed) {
      callback == null ? void 0 : callback();
    }
  }
  /**
   * Analogous to `document.querySelector`, finds the first Bryntum widget matching the passed
   * selector. Right now, only class name (lowercased) selector strings, or
   * a filter function which returns `true` for required object are allowed:
   *
   * ```javascript
   * Widget.query('grid').destroy();
   * ```
   *
   * @param {String|Function} selector A lowercased class name, or a filter function.
   * @param {Boolean} [deep] Specify `true` to search the prototype chain (requires supplying a string `selector`). For
   * example 'widget' would then find a Grid
   * @returns {Core.widget.Widget} The first matched widget if any.
   * @category Widget hierarchy
   */
  static query(selector, deep = false) {
    const { idMap } = _Widget.identifiable;
    for (const id2 in idMap) {
      if (_Widget.widgetMatches(idMap[id2], selector, deep)) {
        return idMap[id2];
      }
    }
    return null;
  }
  /**
   * Analogous to document.querySelectorAll, finds all Bryntum widgets matching the passed
   * selector. Right now, only registered widget `type` strings, or a filter function which
   * returns `true` for required object are allowed:
   *
   * ```javascript
   * let allFields = Widget.queryAll('field', true);
   * ```
   *
   * @param {String|Function} selector A lowercased class name, or a filter function.
   * @param {Boolean} [deep] Specify `true` to search the prototype chain (requires supplying a string `selector`). For
   * example 'widget' would then find a Grid
   * @returns {Core.widget.Widget[]} The first matched widgets if any - an empty array will be returned
   * if no matches are found.
   * @category Widget hierarchy
   */
  static queryAll(selector, deep = false) {
    const { idMap } = _Widget.identifiable, result = [];
    for (const id2 in idMap) {
      if (_Widget.widgetMatches(idMap[id2], selector, deep)) {
        result.push(idMap[id2]);
      }
    }
    return result;
  }
  /**
   * Returns the Widget which owns the passed element (or event).
   * @param {HTMLElement|Event} element The element or event to start from
   * @param {String|Function} [type] The type of Widget to scan upwards for. The lowercase
   * class name. Or a filter function which returns `true` for the required Widget
   * @param {HTMLElement|Number} [limit] The number of components to traverse upwards to find a
   * match of the type parameter, or the element to stop at
   * @returns {Core.widget.Widget|null} The found Widget or null
   * @category Misc
   */
  static fromElement(element, type, limit) {
    const typeOfType = typeof type;
    if (element && !element.nodeType) {
      element = element.target;
    }
    if (typeOfType === "number" || type && type.nodeType === Element.ELEMENT_NODE) {
      limit = type;
      type = null;
    }
    let target = element, depth = 0, topmost, cmpId, cmp;
    if (typeof limit !== "number") {
      topmost = limit;
      limit = Number.MAX_VALUE;
    }
    if (typeOfType === "string") {
      type = type.toLowerCase();
    }
    while (target && target.nodeType === Element.ELEMENT_NODE && depth < limit && target !== topmost) {
      cmpId = target.dataset && target.dataset.ownerCmp || target.id;
      if (cmpId) {
        cmp = _Widget.getById(cmpId);
        if (cmp) {
          if (type) {
            if (typeOfType === "function") {
              if (type(cmp)) {
                return cmp;
              }
            } else if (_Widget.widgetMatches(cmp, type, true)) {
              return cmp;
            }
          } else {
            return cmp;
          }
        }
        depth++;
      }
      target = getParent(target);
    }
    return null;
  }
  /**
   * Returns the widget with the specified id.
   * @function getById
   * @param {String} id Id of widget to find
   * @returns {Core.widget.Widget} The widget
   * @static
   * @category Misc
   */
  /**
   * Returns the Widget which owns the passed CSS selector.
   *
   * ```javascript
   * const button = Widget.fromSelector('#my-button');
   * ```
   *
   * @param {String} selector CSS selector
   * @returns {Core.widget.Widget|null} The found Widget or null
   * @category Misc
   */
  static fromSelector(selector) {
    const element = document.querySelector(selector);
    return element ? _Widget.fromElement(element) : null;
  }
  // NOTE: Not named `triggerChange` to not conflict with existing fn on Field
  /**
   * Triggers a 'change' event with the supplied params. After triggering it also calls `onFieldChange()` on each
   * ancestor the implements that function, supplying the same set of params.
   * @param {Object} params Event params, used both for triggering and notifying ancestors
   * @param {Boolean} [trigger] `false` to not trigger, only notifying ancestors
   * @internal
   */
  triggerFieldChange(params, trigger = true) {
    var _a5;
    if (trigger) {
      this.trigger("change", params);
    } else {
      params.source = this;
    }
    (_a5 = this.eachAncestor) == null ? void 0 : _a5.call(this, (ancestor) => {
      var _a6, _b2;
      (_a6 = ancestor.onFieldChange) == null ? void 0 : _a6.call(ancestor, params);
      if ((_b2 = ancestor.isolateFieldChange) == null ? void 0 : _b2.call(ancestor, this)) {
        return false;
      }
    });
  }
  /**
   * Returns `true` if the given `field`'s value change should be isolated (kept hidden by this widget). By default,
   * this method returns the value of {@link Core.widget.Container#config-isolateFields} for all fields.
   * @param {Core.widget.Field} field The field in question.
   * @internal
   */
  isolateFieldChange(field) {
    return this.isolateFields;
  }
  // Sets up the focus listeners, one set for every document root (shadow root or document)
  setupFocusListeners() {
    var _a5, _b2;
    GlobalEvents_default.setupFocusListenersOnce(this.eventRoot, EventHelper);
    (_b2 = (_a5 = _Widget.Ripple) == null ? void 0 : _a5.setupRippleListenerOnce) == null ? void 0 : _b2.call(_a5);
  }
  static widgetMatches(candidate, selector, deep) {
    if (selector === "*") {
      return true;
    }
    if (typeof selector === "function") {
      return selector(candidate);
    }
    return _Widget.isType(candidate, selector, deep);
  }
  /**
   * Attached a tooltip to the specified element.
   *
   * ```javascript
   * Widget.attachTooltip(element, {
   *   text: 'Useful information goes here'
   * });
   * ```
   *
   * @param {HTMLElement} element Element to attach tooltip for
   * @param {TooltipConfig|String} configOrText Tooltip config or tooltip string, see example and source
   * @returns {HTMLElement} The passed element
   * @category Misc
   */
  static attachTooltip(element, configOrText) {
    if (typeof configOrText === "string")
      configOrText = { html: configOrText };
    _Widget.create(Object.assign({
      forElement: element
    }, configOrText), "tooltip");
    return element;
  }
  // Convenience fn to replace WidgetHelper.append in tests
  static append(items2, config = {}) {
    if (config instanceof HTMLElement || typeof config === "string") {
      config = {
        appendTo: config
      };
    }
    return ArrayHelper.asArray(items2).map((item) => _Widget.create(ObjectHelper.assign({}, config, item)));
  }
  //region RTL
  // Since we use flexbox docking flips correctly out of the box. start and end values can be mapped straight to
  // left and right, for both LTR and RTL
  changeDock(dock) {
    if (dock === "start") {
      return "left";
    }
    if (dock === "end") {
      return "right";
    }
    return dock;
  }
  updateRtl(rtl) {
    super.updateRtl(rtl);
    this.eachWidget((item) => {
      if (!("rtl" in item.initialConfig)) {
        item.rtl = rtl;
      }
    });
    this.ownedWidgets && Object.values(this.ownedWidgets).forEach((widget) => {
      if (!("rtl" in widget.initialConfig)) {
        widget.rtl = rtl;
      }
    });
  }
  // If implementer did not specify a scrollAction, realign anchored floaters by default
  get scrollAction() {
    let { _scrollAction } = this;
    if (!_scrollAction && this.anchor) {
      _scrollAction = "realign";
    }
    return _scrollAction;
  }
  //endregion
  /**
   * Announces a text message that can be parsed by assistive technologies using an `aria-live` element.
   * @param {String} message The message to announce
   */
  announceAriaLive(message = "", live = "polite") {
    var _a5;
    const ariaLiveEl = this.ariaLiveEl || (this.ariaLiveEl = DomHelper.createElement({
      parent: this.element,
      className: "b-arialive-el",
      "aria-live": live,
      "aria-atomic": true
    }));
    if ((_a5 = this.element) == null ? void 0 : _a5.isConnected) {
      const content = message.children ? message.children.reduce(extractChildrenText, []).join(" ") : message.text || message;
      ariaLiveEl.textContent = content;
    }
  }
};
/**
 * Class name getter.
 * Used when original ES6 class name is minified or mangled during production build.
 * Should be overridden in each class which extends Widget or it descendants.
 *
 * ```javascript
 * class MyNewClass extends Widget {
 *     static $name = 'MyNewClass';
 * }
 * ```
 *
 * @static
 * @member {String} $name
 * @advanced
 */
__publicField(_Widget, "$name", "Widget");
/**
 * Widget name alias which you can use in the `items` of a Container widget.
 *
 * ```javascript
 * class MyWidget extends Widget {
 *     static get type() {
 *        return 'mywidget';
 *     }
 * }
 * ```
 *
 * ```javascript
 * const panel = new Panel({
 *    title : 'Cool widgets',
 *    items : {
 *       customWidget : { type : 'mywidget', html : 'Lorem ipsum dolor sit amet...' }
 *    }
 * });
 * ```
 *
 * @static
 * @member {String} type
 */
__publicField(_Widget, "type", "widget");
/**
 * By default, on touch devices, a two finger pinch gesture where both touch points are
 * within a Bryntum widget is converted to a `CTRL/wheel` event injected at the mid-point between
 * the two initial touches.
 *
 * This is so that Scheduler and Calendar views which are configured to `zoomOnMouseWheel` will
 * zoom on pinch on touch devices.
 *
 * That disables the platform's native pinch-zooming. Set this static flag to `false` to disable
 * the conversion:
 *
 * ```javascript
 *     Widget.convertPinchToMousewheel = false;
 * ```
 *
 * @member {Boolean} convertPinchToMousewheel
 * @static
 */
__publicField(_Widget, "convertPinchToMousewheel", true);
var Widget = _Widget;
var proto2 = Widget.prototype;
["compose", "domSyncCallback"].forEach((fn2) => proto2[fn2].$nullFn = true);
Widget.initClass();
DomHelper.Widget = Widget;
GlobalEvents_default.Widget = Widget;
Object.assign(globalThis.bryntum || (globalThis.bryntum = {}), {
  Widget,
  // globalThis.$0 is the currently selected element in Chrome DevTools
  get: (id2 = globalThis.$0) => id2.nodeType ? Widget.fromElement(id2) : Widget.getById(id2),
  query: Widget.query,
  queryAll: Widget.queryAll,
  fromElement: Widget.fromElement,
  fromSelector: Widget.fromSelector
});
Widget._$name = "Widget";

// ../Core/lib/Core/widget/layout/Layout.js
var Layout = class extends Base.mixin(Events_default, Factoryable_default) {
  get contentElement() {
    var _a5;
    return (_a5 = this.owner) == null ? void 0 : _a5.contentElement;
  }
  onChildAdd(item) {
  }
  onChildRemove(item) {
  }
  renderChildren() {
    const me = this, { owner, itemCls: itemCls2 } = me, { contentElement, items: items2 } = owner, ownerItemCls = owner.itemCls, itemCount = items2 == null ? void 0 : items2.length;
    if (itemCount) {
      owner.textContent = false;
      for (let insertion, renderTo, i = 0; i < itemCount; i++) {
        const item = items2[i], { element, positionable } = item, { classList } = element;
        element.dataset.itemIndex = i;
        if (classList.contains("b-outer")) {
          classList.remove(...Widget.outerCls);
        }
        if (itemCls2) {
          classList.add(itemCls2);
        }
        if (ownerItemCls) {
          classList.add(ownerItemCls);
        }
        renderTo = contentElement;
        if (positionable) {
          insertion = owner.getPositionableLocation(positionable);
          if (!(renderTo = insertion[0])) {
            renderTo = insertion[1].parentElement;
            item.insertBefore = insertion[1];
          }
        }
        item.render(renderTo, Boolean(owner.isPainted));
      }
    }
    me.syncPendingConfigs();
    me.syncChildCount();
  }
  removeChild(child) {
    const me = this, { element } = child, { owner, itemCls: itemCls2 } = me, { contentElement } = owner, ownerItemCls = owner.itemCls;
    if (contentElement.contains(element)) {
      element.remove();
    }
    delete element.dataset.itemIndex;
    if (itemCls2) {
      element.classList.remove(itemCls2);
    }
    if (ownerItemCls) {
      element.classList.remove(ownerItemCls);
    }
    me.fixChildIndices();
    me.syncChildCount();
  }
  appendChild(child) {
    const { element } = child, { owner, itemCls: itemCls2 } = this, { contentElement } = owner, ownerItemCls = owner.itemCls;
    element.dataset.itemIndex = owner.indexOfChild(child);
    owner.textContent = false;
    if (itemCls2) {
      element.classList.add(itemCls2);
    }
    if (ownerItemCls) {
      element.classList.add(ownerItemCls);
    }
    child.render(contentElement, Boolean(owner.isPainted));
    this.syncChildCount();
  }
  insertChild(toAdd, childIndex) {
    const me = this, { element } = toAdd, { owner, itemCls: itemCls2 } = me, { contentElement } = owner, nextSibling = DomHelper.getChild(contentElement, `[data-item-index="${childIndex}"]`), ownerItemCls = owner.itemCls;
    owner.textContent = false;
    if (itemCls2) {
      element.classList.add(itemCls2);
    }
    if (ownerItemCls) {
      element.classList.add(ownerItemCls);
    }
    const isMove = contentElement.contains(element);
    contentElement.insertBefore(element, nextSibling);
    if (!isMove) {
      toAdd.render(null, Boolean(owner.isPainted));
    }
    me.fixChildIndices();
    me.syncChildCount();
  }
  fixChildIndices() {
    this.owner.items.forEach((child, index) => {
      child.element.dataset.itemIndex = index;
    });
  }
  syncChildCount() {
    var _a5;
    const { owner } = this, { length } = owner.items;
    (_a5 = owner.contentElement) == null ? void 0 : _a5.classList[length === 1 ? "add" : "remove"]("b-single-child");
  }
  /**
   * Registers a layout `config` property that cannot be acted upon at this time but must wait for the `owner` to
   * fully render its elements (in particular the `contentElement`).
   * @param {String} config The name of the config to sync later.
   * @internal
   */
  syncConfigLater(config) {
    const pendingConfigs = this.pendingConfigs || (this.pendingConfigs = []);
    if (!pendingConfigs.includes(config)) {
      pendingConfigs.push(config);
    }
  }
  /**
   * Sets the specified `style` to the value of the config given its `name`.
   * @param {Object} options The name of the config with the value to apply to the given `style`.
   * @param {String} options.name The name of the config with the value to apply to the given `style`.
   * @param {String} options.style The style property to set on the `contentElement`.
   * @param {String[]} [options.classes] A list of config values that should be added as CSS classes.
   * @param {Object} [options.map] An mapping object to convert the config's value to the `style` value.
   * @internal
   */
  syncConfigStyle({ name, style, classes, map: map2 }) {
    const me = this, baseCls = `b-box-${name}-`, { contentElement } = me, raw = me[name];
    let value2 = (map2 == null ? void 0 : map2[raw]) || raw;
    if (contentElement) {
      if (classes) {
        const { classList } = contentElement;
        classes.forEach((c) => {
          if (c !== value2) {
            classList.remove(baseCls + c);
          }
        });
        if (classes.includes(value2)) {
          classList.add(baseCls + value2);
          value2 = "";
        }
      }
      contentElement.style[style] = value2;
    } else {
      me.syncConfigLater(name);
    }
  }
  syncPendingConfigs() {
    const me = this, { pendingConfigs } = me;
    let name;
    if (pendingConfigs) {
      me.pendingConfigs = null;
      while (name = pendingConfigs.pop()) {
        me[me.$meta.configs[name].updater](me[name]);
      }
    }
  }
};
__publicField(Layout, "type", "default");
__publicField(Layout, "configurable", {
  /**
   * The owning Widget.
   * @member {Core.widget.Widget} owner
   * @readonly
   */
  /**
   * @config {Core.widget.Widget} owner
   * @private
   */
  owner: null,
  /**
   * The CSS class which should be added to the owning {@link Core.widget.Container}'s.
   * {@link Core.widget.Widget#property-contentElement}.
   * @config {String}
   */
  containerCls: "b-auto-container",
  /**
   * The CSS class which should be added to the encapsulating element of child items.
   * @config {String}
   */
  itemCls: null
});
// establish this class as the Factoryable base
__publicField(Layout, "factoryable", {
  defaultType: "default"
});
Layout.initClass();
Layout._$name = "Layout";

// ../Core/lib/Core/widget/Ripple.js
var lastTouchTime = 0;
var hasRipple = (w) => w.ripple;
var Ripple = class extends Widget {
  static get defaultConfig() {
    return {
      old_element: {
        children: [{
          className: "b-ripple-inner",
          reference: "rippleElement"
        }]
      },
      element: {
        children: [{
          tag: "svg",
          class: "b-ripple-inner",
          reference: "rippleElement",
          ns: "http://www.w3.org/2000/svg",
          version: "1.1",
          viewBox: "0 0 100 100",
          children: [{
            reference: "circleElement",
            tag: "circle",
            cx: "0",
            cy: "0",
            r: 10
          }]
        }]
      },
      floating: true,
      hideAnimation: false,
      showAnimation: false,
      color: "rgba(0,0,0,.3)",
      startRadius: 10,
      radius: 100
    };
  }
  afterConstruct() {
    super.afterConstruct();
    this.setupRippleListenerOnce();
  }
  setupRippleListenerOnce() {
    if (!this.rippleControlEventDetacher) {
      this.rippleControlEventDetacher = EventHelper.on({
        element: this.rootElement,
        mousedown: "onRippleControllingEvent",
        thisObj: this,
        capture: true,
        once: true
      });
    }
  }
  cleanUpRippleListeners() {
    var _a5, _b2;
    (_a5 = this.rippleControlEventDetacher) == null ? void 0 : _a5.call(this);
    (_b2 = this.listenerDetacher) == null ? void 0 : _b2.call(this);
    this.rippleControlEventDetacher = null;
    this.listenerDetacher = null;
  }
  onRippleControllingEvent(event) {
    var _a5;
    const me = this;
    me.show();
    const rippleAnimation = DomHelper.getStyleValue(me.circleElement, "animationName");
    me.hide();
    (_a5 = me.listenerDetacher) == null ? void 0 : _a5.call(me);
    if (rippleAnimation && rippleAnimation !== "none") {
      me.listenerDetacher = EventHelper.on({
        // Trap all mousedowns and see if the encapsulating Component is configured to ripple
        mousedown: {
          element: me.rootElement,
          capture: true,
          handler: "onMousedown"
        },
        touchstart: {
          element: me.rootElement,
          capture: true,
          handler: "onTouchStart"
        },
        // Hide at the end of the ripple
        animationend: {
          element: me.circleElement,
          handler: "onAnimationEnd"
        },
        thisObj: me
      });
      if (event.type === "mousedown") {
        me.onMousedown(event);
        me.listenerDetacher = FunctionHelper.createSequence(
          me.listenerDetacher,
          GlobalEvents_default.ion({
            theme: "onRippleControllingEvent",
            thisObj: me
          }),
          me,
          me
        );
      }
    }
  }
  onTouchStart(event) {
    lastTouchTime = performance.now();
    this.handleTriggerEvent(event);
  }
  onMousedown(event) {
    if (performance.now() - lastTouchTime > 200) {
      this.handleTriggerEvent(event);
    }
  }
  handleTriggerEvent(event) {
    const targetWidget = Widget.fromElement(event.target, hasRipple);
    if (targetWidget) {
      const rippleCfg = targetWidget.ripple, target = rippleCfg.delegate ? event.target.closest(rippleCfg.delegate) : targetWidget.focusElement || targetWidget.element;
      if (target) {
        const ripple = ObjectHelper.assign({
          event,
          target,
          radius: this.radius
        }, rippleCfg);
        if (typeof ripple.clip === "string") {
          ripple.clip = targetWidget[ripple.clip] || event.target.closest(ripple.clip);
          if (!ripple.clip) {
            return;
          }
        }
        this.ripple(ripple);
      }
    }
  }
  ripple({
    event,
    point = EventHelper.getClientPoint(event),
    target = event.target,
    clip = target,
    radius = this.radius,
    color = this.color
  }) {
    this.clip = clip;
    clip = Rectangle.from(clip, null, true);
    const me = this, centreDelta = clip.getDelta(point), rippleStyle = me.rippleElement.style, circleElement = me.circleElement, borderRadius = DomHelper.getStyleValue(target, "border-radius");
    me.hide();
    rippleStyle.transform = `translateX(${centreDelta[0]}px) translateY(${centreDelta[1]}px)`;
    rippleStyle.height = rippleStyle.width = `${radius}px`;
    me.element.style.borderRadius = borderRadius;
    circleElement.setAttribute("r", radius);
    circleElement.setAttribute("fill", color);
    me.showBy({
      target: clip,
      align: "c-c",
      matchSize: true
    });
  }
  // When fully expanded, it's all over.
  onAnimationEnd(event) {
    if (event.animationName === "b-ripple-expand") {
      this.hide();
    }
  }
};
__publicField(Ripple, "$name", "Ripple");
__publicField(Ripple, "type", "ripple");
Widget.RippleClass = Ripple;
Ripple._$name = "Ripple";

// ../Core/lib/Core/widget/Mask.js
var isActive = (mask) => mask.element.classList.contains("b-visible");
var isPending = (mask) => !isActive(mask) && mask.isVisible;
var _Mask = class _Mask extends Widget {
  /**
   * Returns the array of `Mask` instances for the given `element`.
   * @param {HTMLElement} element The element for which to return `Mask` instances
   * @returns {Core.widget.Mask|Core.widget.Mask[]}
   */
  static get(element) {
    return DomDataStore.get(element, "masks") || DomDataStore.set(element, "masks", []);
  }
  static getActive(element) {
    return _Mask.get(element).find(isActive) || null;
  }
  static getPending(element) {
    return _Mask.get(element).filter(isPending).sort(Widget.weightSortFn);
  }
  /**
   * Shows a mask with the specified message.
   *
   * Masks stack, call {@link #function-unmask-static} to remove the topmost mask. Or call {@link #function-close}
   * on the returned mask to close it specifically. Only one mask is displayed for a given `target` element at a time.
   * For example, if two masks are added to an element, the first mask is displayed. If the first mask is closed,
   * then the second mask will become visible.
   *
   * @param {String|MaskConfig} text Message
   * @param {HTMLElement} [target] The element to mask (default is `document.body`)
   * @returns {Core.widget.Mask}
   */
  static mask(text, target = document.body) {
    return _Mask.new({ target }, text);
  }
  static mergeConfigs(...sources) {
    sources = sources.map((c) => typeof c === "string" ? { text: c } : c);
    return super.mergeConfigs(...sources);
  }
  static sync(target) {
    const active = _Mask.getActive(target), pending = !active && _Mask.getPending(target)[0] || null, mask = active || pending, mode = (mask == null ? void 0 : mask.mode) || "", cls = mode.endsWith("blur") ? `b-masked-${mode}` : "";
    let child, classList, remove;
    if (pending) {
      classList = pending.element.classList;
      if (pending.showDelay && !target.classList.contains("b-masked")) {
        classList.add("b-delayed-show");
        pending.delayedShow();
      }
      classList.add("b-visible");
    }
    target.classList.toggle("b-masked", Boolean(mask));
    for (child of target.children) {
      classList = child.classList;
      if (!classList.contains("b-mask")) {
        remove = Array.from(classList).filter((c) => c.startsWith("b-masked-") && cls !== c);
        remove.length && classList.remove(...remove);
        cls && classList.add(cls);
      }
    }
  }
  /**
   * Close the most recently created mask for the specified element.
   * @param {HTMLElement} [element] The element to unmask (default is `document.body`)
   * @returns {Promise|null} A promise which is resolved when the mask is gone, or null if element is not masked
   */
  static unmask(element = document.body) {
    var _a5;
    const masks = _Mask.get(element), n = masks.length;
    return n ? (_a5 = masks[n - 1]) == null ? void 0 : _a5.close() : null;
  }
  /**
   * Close all masks for the specified element
   * @param {HTMLElement} [element] The element to unmask (default is `document.body`)
   * @returns {Promise|null} A promise which is resolved when the mask is gone, or null if element is not masked
   * @internal
   */
  static unmaskAll(element = document.body) {
    _Mask.get(element).filter((mask) => !isActive(mask)).forEach((m) => m.destroy());
    return _Mask.unmask(element);
  }
  //endregion
  //region Init
  destroy() {
    if (this.type !== "trial") {
      super.destroy();
    }
  }
  compose() {
    const { icon, maxProgress, mode, progress, showDelay, text, html, useTransition } = this;
    return {
      class: {
        "b-mask": 1,
        "b-delayed-show": showDelay,
        "b-progress": maxProgress,
        "b-prevent-transitions": !useTransition,
        // 'b-hidden'              : hidden,
        // 'b-visible'             : !hidden,
        [`b-mask-${mode}`]: 1
      },
      children: {
        maskContent: {
          class: "b-mask-content",
          children: {
            progressElement: maxProgress ? {
              class: "b-mask-progress-bar",
              style: {
                width: `${Math.max(0, Math.min(100, Math.round(progress / maxProgress * 100)))}%`
              }
            } : null,
            maskText: {
              class: "b-mask-text",
              html: `${icon ? `${`<i class="b-mask-icon ${icon}"></i>`}` : ""}${text || html || ""}`
            }
          }
        }
      },
      // eslint-disable-next-line bryntum/no-listeners-in-lib
      listeners: {
        mousedown: "onMaskMouseDown",
        capture: true
      }
    };
  }
  onMaskMouseDown(e) {
    e.preventDefault();
  }
  //endregion
  //region Config
  generateAutoId() {
    const { type } = this;
    return `mask${typeof type === "string" ? type.trim() : ""}-${_Mask.counter++}`;
  }
  updateAutoClose(delay2) {
    this.deferredClose.cancel();
    if (delay2) {
      this.deferredClose.delay = delay2;
      this.deferredClose();
    }
  }
  updateCover() {
    this.syncCover();
  }
  syncCover() {
    var _a5, _b2;
    (_b2 = (_a5 = this.owner) == null ? void 0 : _a5.syncMaskCover) == null ? void 0 : _b2.call(_a5, this);
  }
  set error(value2) {
    this.setConfig({
      ...this.errorDefaults,
      text: value2
    });
  }
  onOwnerResize() {
    this.syncCover();
  }
  updateOwner(owner) {
    this.detachListeners("cover");
    owner == null ? void 0 : owner.ion({
      name: "cover",
      recompose: "onOwnerResize",
      resize: "onOwnerResize",
      thisObj: this
    });
  }
  updateShowDelay(delay2) {
    const { delayedShow } = this;
    delayedShow.delay = delay2;
    if (!delay2) {
      delayedShow.flush();
    }
  }
  changeTarget(target) {
    if (target === void 0) {
      target = document.body;
    } else if (typeof target === "string") {
      target = this.owner[target];
    }
    return target;
  }
  updateTarget(target, was) {
    const me = this, { id: id2, element } = me, masks = was && _Mask.get(was);
    if (was) {
      if (was[id2] === me) {
        delete was[id2];
      }
      ArrayHelper.remove(masks, me);
      _Mask.sync(was);
    }
    if (target) {
      if (!target[id2]) {
        target[id2] = me;
      }
      if (element.parentNode !== target) {
        target.appendChild(element);
      }
      ArrayHelper.include(_Mask.get(target), me);
      _Mask.sync(target);
    } else {
      element.remove();
    }
  }
  //endregion
  //region Show & hide
  deferredClose() {
    var _a5;
    const me = this, { owner } = me;
    me.close().then(() => {
      var _a6;
      return (_a6 = owner == null ? void 0 : owner.onMaskAutoClose) == null ? void 0 : _a6.call(owner, me);
    });
    (_a5 = owner == null ? void 0 : owner.onMaskAutoClosing) == null ? void 0 : _a5.call(owner, me);
  }
  delayedShow() {
    this.element.classList.remove("b-delayed-show");
  }
  updateHidden(hidden, was) {
    var _a5;
    (_a5 = super.updateHidden) == null ? void 0 : _a5.call(this, hidden, was);
    hidden && this.element.classList.remove("b-visible");
    _Mask.sync(this.target);
  }
  afterShow(...args) {
    var _a5;
    (_a5 = super.afterShow) == null ? void 0 : _a5.call(this, ...args);
    _Mask.sync(this.target);
  }
  afterHide(...args) {
    var _a5;
    (_a5 = super.afterHide) == null ? void 0 : _a5.call(this, ...args);
    this.element.classList.remove("b-visible");
    _Mask.sync(this.target);
  }
  /**
   * Close mask (removes it)
   * @returns {Promise} A promise which is resolved after the mask is closed and destroyed.
   */
  async close() {
    await this.hide();
    this.destroy();
  }
  //endregion
};
//region Config
__publicField(_Mask, "$name", "Mask");
__publicField(_Mask, "type", "mask");
__publicField(_Mask, "configurable", {
  /**
   * Set this config to trigger an automatic close after the desired delay:
   *
   * ```javascript
   *  mask.autoClose = 2000;
   * ```
   *
   * If the mask has an `owner`, its `onMaskAutoClosing` method is called when the close starts and its
   * `onMaskAutoClose` method is called when the close finishes.
   * @config {Number}
   * @private
   */
  autoClose: null,
  /**
   * The portion of the {@link #config-target} element to be covered by this mask. By default, the mask fully
   * covers the `target`. In some cases, however, it may be desired to only cover the `'body'` (for example,
   * in a grid).
   *
   * This config is set in conjunction with `owner` which implements the method `syncMaskCover`.
   *
   * @config {String}
   * @private
   */
  cover: null,
  /**
   * The icon to show next to the text. Defaults to showing a spinner
   * @config {String}
   * @default
   */
  icon: "b-icon b-icon-spinner",
  errorDefaults: {
    icon: "b-icon b-icon-warning",
    autoClose: 3e3,
    showDelay: 0
  },
  /**
   * The maximum value of the progress indicator
   * @property {Number}
   */
  maxProgress: null,
  /**
   * Mode: bright, bright-blur, dark or dark-blur
   * @config {'bright'|'bright-blur'|'dark'|'dark-blur'}
   * @default
   */
  mode: "dark",
  /**
   * Number expressing the progress
   * @property {Number}
   */
  progress: null,
  // The owner is involved in the following features:
  //
  // - The `autoClose` timer calls `onMaskAutoClose`.
  // - The `cover` config calls `syncMaskCover`.
  // - If the `target` is a string, that string names the property of the `owner` that holds the
  //   `HTMLElement` reference.
  /**
   * The element to be masked. If this config is a string, that string is the name of the property of the
   * {@link #config-owner} that holds the `HTMLElement` that is the actual target of the mask.
   *
   * NOTE: In prior releases, this used to be specified as the `element` config.
   *
   * @config {String|HTMLElement}
   */
  target: {
    $config: "nullify",
    value: void 0
  },
  /**
   * The text (or HTML) to show in mask
   * @prp {String}
   */
  text: null,
  type: null,
  /**
   * The number of milliseconds to delay before making the mask visible. If set, the mask will have an
   * initial `opacity` of 0 but will function in all other ways as a normal mask. Setting this delay can
   * reduce flicker in cases where load operations are typically short (for example, a second or less).
   *
   * @config {Number}
   */
  showDelay: null,
  useTransition: false
});
__publicField(_Mask, "delayable", {
  deferredClose: 0,
  delayedShow: 0,
  syncCover: {
    type: "throttle",
    delay: 100
  }
});
//endregion
//region Static
// Used to give masks unique names
__publicField(_Mask, "counter", 0);
var Mask = _Mask;
Mask.initClass();
Mask._$name = "Mask";

// ../Core/lib/Core/widget/Container.js
var emptyObject7 = Object.freeze({});
var { isArray: isArray2 } = Array;
var returnWeight = (i) => i.weight;
var isNotHidden = (w) => w && !w.hidden;
var isFocusable = (w) => w.isFocusable;
var stylesToCheck = ["display", "flex-direction"];
var boxLayouts = {
  default: 1,
  box: 1
};
var optionsProp = Symbol("options");
var Container = class extends Widget {
  static get configurable() {
    return {
      /**
       * An object containing typed child widget config objects or Widgets. May also be specified
       * as an array.
       *
       * If configured as an Object, the property names are used as the child component's
       * {@link Core.widget.Widget#config-ref} name, and the value is the child component's config object.
       *
       * ```javascript
       *  class MyContainer extends Container {
       *      static configurable = {
       *          items : {
       *              details : {
       *                  type : 'panel',
       *                  ....
       *              },
       *              button : {
       *                  type : 'button',
       *                  text : 'Save'
       *              }
       *          }
       *      }
       *  }
       *
       *  new MyContainer({
       *      title    : 'Test Container',
       *      floating : true,
       *      centered : true,
       *      width    : 600,
       *      height   : 400,
       *      layout   : 'fit',
       *      items    : {
       *          button : {
       *              disabled : true
       *          },
       *          details : {
       *              title : 'More coolness',
       *              html  : 'Details content'
       *          }
       *      }
       *  }).show();
       * ```
       *
       * The order of the child widgets is determined by the order they are defined in `items`, but can also be
       * affected by configuring a {@link Core.widget.Widget#config-weight} on one or more widgets.
       *
       * To remove existing items, set corresponding keys to `null`.
       *
       * If you want to customize child items of an existing class, you can do this using the child widget
       * 'ref' identifier (useful for reconfiguring Event Editor in Scheduler / Gantt):
       *
       * ```javascript
       *  new MyCustomTabPanel({
       *      items    : {
       *          // Reconfigure tabs
       *          firstTab : {
       *              title : 'My custom title'
       *          },
       *          secretTab : null // hide this tab
       *      }
       *  }).show();
       * ```
       *
       * {@note} Note that conversion between array and object notation is not supported, if you want to override
       * items in a container that uses object notation, you must use the same notation. This applies for example
       * to when customizing items in the event editor in Scheduler and the task editor in Scheduler Pro and
       * Gantt.{/@note}
       *
       * @config {Object<String,ContainerItemConfig|MenuItemConfig|Boolean|null>|Array<ContainerItemConfig|MenuItemConfig|Core.widget.Widget>}
       * @category Content
       */
      items: null,
      /**
       * An array of {@link #config-items child item} _config objects_ which is to be converted into
       * instances only when this Container is rendered, rather than eagerly at construct time.
       *
       * _This is mutually exclusive with the {@link #config-items} config._
       *
       * @config {Object<String,ContainerItemConfig>|ContainerItemConfig[]|Core.widget.Widget[]}
       * @category Content
       */
      lazyItems: {
        $config: ["lazy"],
        value: null
      },
      /**
       * When this container is used as a tab in a TabPanel, these items are added to the
       * {@link Core/widget/TabBar} when this container is the active tab.
       *
       * ```javascript
       * new TabPanel({
       *     appendTo : targetElement,
       *     height   : '25em',
       *     items: {
       *         firstTab : {
       *             title: 'First',
       *             items: {
       *                 name : { type: 'textfield', label: 'Name' }
       *             },
       *             tabBarItems : [{
       *                 type    : 'button',
       *                 text    : 'Add tab',
       *                 onClick : 'up.onAddTabClick'
       *             }]
       *         }
       *     },
       *
       *     onAddTabClick({ value }) {
       *         this.add({
       *             type  : 'container',
       *             title : 'New tab',
       *             items : {
       *                 button : {
       *                     type : 'button',
       *                     text : 'Click me',
       *                     onClick() {
       *                         Toast.show('Awesome!');
       *                     }
       *                 }
       *             }
       *         });
       *     }
       * });
       * ```
       *
       * @config {ToolbarItems[]|Core.widget.Widget[]} tabBarItems
       * @category misc
       */
      tabBarItems: null,
      /**
       * A config object containing default settings to apply to all child widgets.
       * @config {ContainerItemConfig}
       * @category Content
       */
      defaults: null,
      defaultType: "widget",
      /**
       * The CSS style properties to apply to the {@link Core.widget.Widget#property-contentElement}.
       *
       * By default, a Container's {@link Core.widget.Widget#property-contentElement} uses flexbox layout, so this
       * config may contain the following properties:
       *
       * - <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/flex-direction">flexDirection</a> default '`row`'
       * - <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/flex-wrap">flexWrap</a>
       * - <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/flex-flow">flexFlow</a>
       * - <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/justify-content">justifyContent</a>
       * - <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/align-items">alignItems</a>
       * - <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/align-content">alignContent</a>
       * - <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/place-content">placeContent</a>
       * @prp {Object}
       * @category Layout
       */
      layoutStyle: null,
      /**
       * An optional CSS class to add to child items of this container.
       * @config {String}
       * @category CSS
       */
      itemCls: null,
      /**
       * The {@link #config-layout} as an instance of {@link Core.widget.layout.Layout}.
       * This is a helper class which adds and removes child widgets to this Container's
       * DOM and applies CSS classes based upon its requirements.
       *
       * The {@link Core.widget.layout.Card card} layout provides for showing one child
       * widget at a time, and provides a switching API to change which child widget is
       * currently active.
       * @member {Core.widget.layout.Layout} layout
       * @category Layout
       */
      /**
       * The short name of a helper class which manages rendering and styling of child items.
       *
       * Or a config object which includes a `type` property which specifies which type
       * of layout to use, and how to configure that layout.
       *
       * By default, the only special processing that is applied is that the Container class's
       * {@link #config-itemCls} is added to child items.
       *
       * Containers use CSS flexbox in its default configuration to arrange child items. You may either use the
       * {@link #config-layoutStyle} configuration to tune how child items are layed out, or use one of the built
       * in helper classes which include:
       *
       *  - `fit` A single child item is displayed fitting exactly into the
       *  {@link Core.widget.Widget#property-contentElement}.
       *  - `card` Child items are displayed one at a time, size to fit the
       *  {@link Core.widget.Widget#property-contentElement} and are slid in from the side when activated.
       *  - `box` Child items are layed out using flexbox.
       *
       * For example:
       *
       * ```javascript
       * {
       *     id     : 'myContainer',
       *     // Our child items flow downwards and are stretched to fill our width
       *     layout : {
       *         type       : 'box',
       *         direction  : 'column'
       *         align      : 'stretch'
       *     }
       * }
       * ```
       *
       * @config {String|ContainerLayoutConfig}
       * @category Layout
       */
      layout: {
        type: "default"
      },
      /**
       * An object containing default config objects which may be referenced by name in the {@link #config-items}
       * config. For example, a specialized {@link Core.widget.Menu Menu} subclass may have a `namedItems` default
       * value defined like this:
       *
       * ```javascript
       *  namedItems : {
       *      removeRow : {
       *          text : 'Remove row',
       *          onItem() {
       *              this.ownerGrid.remove(this.ownerGrid.selectedRecord);
       *          }
       *      }
       *  }
       * ```
       *
       * Then whenever that subclass is instantiated and configured with an {@link #config-items} object, the
       * `items` may use the above defaults like this:
       *
       * ```javascript
       *  items : {
       *      removeRow : true,   // The referenced namedItem will be applied to this
       *      otherItemRef : {
       *          text : 'Option 2',
       *          onItem() { }
       *      }
       * }
       * ```
       * If a menu instance (or its class) does not include `removeRow` in its `items`, no menu item will be
       * created. See also {@link #config-defaults}.
       *
       * @config {Object<string,ContainerItemConfig>}
       * @category Content
       */
      namedItems: {
        $config: "lazy",
        value: null
      },
      /**
       * When set to `true`, this widget is considered as a whole when processing {@link Core.widget.Toolbar}
       * overflow. When `false`, this widget's child items are considered instead.
       *
       * When set to the string `'none'`, this widget is ignored by overflow processing. This option should be
       * used with caution as it prevents the overflow algorithm from moving such widgets into the overflow
       * popup which may result in not clearing enough space to avoid overflowing the toolbar.
       * @config {Boolean|String}
       * @default false
       * @category Layout
       * @internal
       */
      overflowable: {
        value: null,
        default: false,
        $config: null
      },
      /**
       * Specify `true` for a container used to show text markup. It will apply the CSS class `b-text-content`
       * which specifies a default max-width that makes long text more readable.
       *
       * This CSS class is automatically removed if the container adds/defines child Widgets.
       * @config {Boolean}
       * @default
       * @category Content
       */
      textContent: true,
      /**
       * {@link Core.data.Model Record} whose values will be used to populate fields in the container.
       *
       * Any descendant widgets of this Container with a `name` property (or a `ref` if no name is configured)
       * will have its value set to the value of that named property of the record.
       *
       * If no record is passed, the widget has its value set to `null`.
       *
       * To strictly match by the `name` property, configure {@link #config-strictRecordMapping} as `true`.
       *
       * When fields loaded from this record are changed by user input, the {@link #property-hasChanges}
       * property will be set.
       *
       * @prp {Core.data.Model}
       * @category Record
       */
      record: null,
      /**
       * Specify `true` to match fields by their `name` property only when assigning a {@link #config-record},
       * without falling back to `ref`.
       *
       * @prp {Boolean}
       * @default false
       * @category Record
       */
      strictRecordMapping: null,
      /**
       * Update assigned {@link #config-record} automatically on field changes
       * @config {Boolean}
       * @category Record
       */
      autoUpdateRecord: null,
      autoUpdateUserChangesOnly: true,
      /**
       * Update fields if the {@link #config-record} changes
       * @config {Boolean}
       * @internal
       */
      autoUpdateFields: null,
      /**
       * Specify `true` to make this container hide when it has no visible children (Either empty
       * or all children hidden).
       *
       * Container will show itself when there are visible children, ie: hidden children are
       * shown, or new visible children are added.
       * @config {Boolean}
       * @default
       * @category Layout
       */
      hideWhenEmpty: null,
      contentElMutationObserver: {
        $config: ["lazy", "nullify"],
        value: true
      },
      /**
       * Specify `true` to isolate record changes to this container and its ancestors. Prevents record updates
       * from propagating up from here and also prevents record updates from parent from propagating down to us.
       *
       * @config {Boolean}
       * @default false
       * @internal
       */
      isolateFields: false,
      /**
       * Can be set to `true` to make a focus of a focusable encapsulating element relay focus down into a
       * focusable child. This is normally `false` to allow mousedown to begin text selection in Popups.
       * @internal
       */
      focusDescendant: false,
      // Our own setValues/getValues system should not set/get HTML content
      defaultBindProperty: null,
      /**
       * A {@link #function-query} selector function which can identify the descendant widget to which
       * focus should be directed by default.
       *
       * Or a CSS selector string which identifies a descendant element to focus.
       *
       * By default, the first focusable descendant widget is chosen. This may direct focus to a different
       * widget:
       *
       * ```javascript
       *     new Popup({
       *         title        : 'Details',
       *         width        : '25em',
       *         centered     : true,
       *         modal        : true,
       *
       *         // Focus goes straight to OK button in the bottom toolbar on show
       *         defaultFocus : w => w.ref === 'okButton',
       *         items        : {
       *             nameField : {
       *                 type  : 'textfield',
       *                 label : 'Name'
       *             },
       *             ageField  : {
       *                 type  : 'numberfield',
       *                 label : 'Name'
       *             }
       *         },
       *         bbar     : {
       *             items : {
       *                 okButton : {
       *                     text    : 'OK',
       *                     handler : okFunction
       *                 },
       *                 cancelButton : {
       *                     text    : 'Cancel',
       *                     handler : cancelFunction
       *                 }
       *             }
       *         }
       *     }).show();
       * ```
       *
       * @config {Function|String}
       * @param {Core.widget.Widget} widget Widget passed to method
       * @returns {Boolean} truthy value if widget is the default one
       */
      defaultFocus: null
    };
  }
  static get prototypeProperties() {
    return {
      // These classes have opinions about how fields should fill the space, so allow them to be replaced by the
      // less opinionated b-hbox/b-vbox classes when that is not desired. Using ":not(.b-toolbar-content)" in
      // the CSS does not scale now that FieldSet wants similar treatment... adding more ":not()"s is not only a
      // messy approach, it increases the selector specificity and causes interference with other selectors (e.g.
      // TimePicker's number field rules).
      flexRowCls: "b-flex-row",
      flexColCls: "b-flex-column",
      /**
       * @member {Boolean} initialItems
       * This property is `true` until the container's initial `items` config has been processed. This property
       * is set to `false` by the `updateItems` method.
       * @readonly
       * @internal
       */
      initialItems: true
    };
  }
  startConfigure(config) {
    const items2 = config.items || config.lazyItems;
    if (!(this.hasItems = Boolean(items2 && (isArray2(items2) ? items2 : Object.keys(items2)).length))) {
      this.initialItems = false;
    }
    super.startConfigure(config);
  }
  /**
   * Returns the first widget in this Container.
   * @property {Core.widget.Widget}
   * @readonly
   */
  get firstItem() {
    return this.getAt(0);
  }
  /**
   * Returns the last widget in this Container.
   * @property {Core.widget.Widget}
   * @readonly
   */
  get lastItem() {
    return this.getAt(-1);
  }
  getPositionableItems(which, keyed) {
    var _a5;
    const items2 = ((_a5 = this._items) == null ? void 0 : _a5.filter((it) => which ? it.positionable === which : it.positionable)) || [];
    return keyed ? Object.fromEntries(items2.map((c, n) => [`$${c.positionable}${n}`, c.element])) : items2;
  }
  /**
   * Returns a 2-element array of `[parentElement, insertBefore]` that specifies where in the DOM to insert a child
   * item based on its {@link Core.widget.Widget#config-positionable} config value.
   * @param {'before'|'after'} position The item's {@link Core.widget.Widget#config-positionable} config value.
   * @returns {HTMLElement[]}
   * @internal
   */
  getPositionableLocation(position) {
    return [this.contentElement];
  }
  /**
   * Returns the widget at the specified `index` in this Container.
   * @param {Number} index The index of the widget to return. Negative numbers index for the last item. For example,
   * `index = -1` returns the last matching item, -2 the 2nd to last matching item etc..
   * @returns {Core.widget.Widget} The requested widget.
   */
  getAt(index) {
    return this.ensureItems().at(index);
  }
  /**
   * Removes the passed child/children from this Container.
   * @param  {...Core.widget.Widget} toRemove The child or children to remove.
   * @returns {Core.widget.Widget|Core.widget.Widget[]} All the removed items. An array if multiple items
   * were removed, otherwise, just the item removed.
   */
  remove(...toRemove) {
    let returnArray = true;
    if (toRemove.length === 1) {
      if (isArray2(toRemove[0])) {
        toRemove = toRemove[0];
      } else {
        returnArray = false;
      }
    }
    const me = this, { _items } = me, result = [];
    for (let i = 0; i < toRemove.length; i++) {
      const childToRemove = toRemove[i];
      if (_items.includes(childToRemove)) {
        _items.remove(childToRemove);
        me.layout.removeChild(childToRemove);
        result.push(childToRemove);
        me.onChildRemove(childToRemove);
      }
    }
    return returnArray ? result : result[0];
  }
  /**
   * Removes all children from this Container.
   * @returns {Core.widget.Widget[]} All the removed items.
   */
  removeAll() {
    return this.remove(this.items);
  }
  /**
   * Appends the passed widget / widgets or config(s) describing widgets to this Container.
   *
   * If the widgets specify a `weight`, they are inserted at the correct index compared to the existing items weights.
   *
   * @param {ContainerItemConfig|ContainerItemConfig[]|Core.widget.Widget|Core.widget.Widget[]} toAdd The child or children instances (or config objects) to add.
   * @returns {Core.widget.Widget|Core.widget.Widget[]} All the added widgets. An array if multiple items
   * were added, otherwise just the item added.
   */
  add(...toAdd) {
    const me = this, items2 = me.ensureItems(), result = [];
    let returnArray = true, childToAdd, i, index;
    if (toAdd.length === 1) {
      if (isArray2(toAdd[0])) {
        toAdd = toAdd[0];
      } else {
        returnArray = false;
      }
    }
    for (i = 0; i < toAdd.length; i++) {
      childToAdd = toAdd[i];
      if (childToAdd.isWidget) {
        childToAdd.parent = me;
      } else {
        childToAdd = me.createWidget(childToAdd);
      }
      if ((childToAdd == null ? void 0 : childToAdd.weight) != null) {
        index = ArrayHelper.findInsertionIndex(childToAdd, items2.values, Widget.weightSortFn);
        result.push(me.insert(childToAdd, index));
      } else if (childToAdd) {
        if (!items2.includes(childToAdd)) {
          items2.add(childToAdd);
          me.onChildAdd(childToAdd);
          me.layout.appendChild(childToAdd);
          result.push(childToAdd);
        }
      }
    }
    return returnArray ? result : result[0];
  }
  ensureItems() {
    const me = this;
    me.getConfig("items");
    me.getConfig("lazyItems");
    if (!me._items) {
      me.items = [];
    }
    return me._items;
  }
  /**
   * Inserts the passed widget into this Container at the specified position.
   * @param  {Core.widget.Widget} toAdd The child to insert.
   * @param {Number|Core.widget.Widget} index The index to insert at or the existing child to insert before.
   * @returns {Core.widget.Widget} The added widget.
   */
  insert(toAdd, index) {
    var _a5;
    const me = this, items2 = me.ensureItems();
    if (toAdd instanceof Widget) {
      toAdd.parent = me;
    } else {
      toAdd = me.createWidget(toAdd);
    }
    if (items2.includes(index)) {
      index = me.indexOfChild(index) - 1;
    }
    index = Math.min(index, items2.count);
    const newValues = items2.values;
    if (newValues.includes(toAdd)) {
      const oldIndex = newValues.indexOf(toAdd);
      newValues.splice(oldIndex, 1);
      newValues.splice(index, 0, toAdd);
      items2.values = newValues;
      me.syncChildCount();
      (_a5 = me.onChildMove) == null ? void 0 : _a5.call(me, toAdd);
      me.layout.insertChild(toAdd, index < oldIndex ? index : index + 1);
    } else {
      newValues.splice(index, 0, toAdd);
      items2.values = newValues;
      me.onChildAdd(toAdd);
      me.layout.insertChild(toAdd, index);
    }
    return toAdd;
  }
  indexOfChild(child) {
    return this.items.indexOf(child);
  }
  changeLazyItems(lazyItems) {
    this.items = lazyItems;
    this.layout.renderChildren();
  }
  onChildWeightChange(changedChild) {
    var _a5;
    if (this.items.length > 1) {
      const { lastItem } = this;
      this._items.sort(Widget.weightSortFn);
      const { items: items2 } = this;
      this.contentElement.insertBefore(changedChild.element, ((_a5 = items2[items2.indexOf(changedChild) + 1]) == null ? void 0 : _a5.element) || lastItem.element.nextSibling);
    }
  }
  changeItems(items2, oldItems) {
    const me = this, newItems = [], result = new Bag();
    if (isArray2(items2)) {
      me.processItemsArray(items2, newItems);
    } else if (items2) {
      me.processItemsObject(items2, me.namedItems, newItems);
    }
    if (newItems.some(returnWeight)) {
      newItems.sort(Widget.weightSortFn);
    }
    result.add(newItems);
    if (oldItems) {
      oldItems.forEach((w) => {
        me.remove(w);
        if (!result.includes(w) && w._createdBy === me) {
          w.destroy();
        }
      });
    }
    return result;
  }
  afterConstruct() {
    const { rtl } = this, { classList } = this.contentElement;
    classList.toggle("b-rtl", rtl === true);
    classList.toggle("b-ltr", rtl === false);
  }
  updateRtl(rtl) {
    super.updateRtl(rtl);
    const { contentElement } = this;
    if (contentElement) {
      contentElement.classList.toggle("b-rtl", rtl === true);
      contentElement.classList.toggle("b-ltr", rtl === false);
    }
  }
  updateItems(items2, oldItems) {
    let index = 0;
    items2.forEach((item) => {
      this.onChildAdd(item);
      if (oldItems && !oldItems.temporary) {
        this.layout.insertChild(item, index);
      }
      ++index;
    });
    this.initialItems = false;
  }
  updateHideWhenEmpty() {
    this.syncChildCount(this.rendered);
  }
  /**
   * A property, which, when *read*, returns an array of the child items of this container in rendered order.
   *
   * This property may also be *set* to change the child items of the container. Just as in the
   * {@link #config-items initial items configuration}, the new value may either be an array of
   * Widgets/Widget configs or an object.
   *
   * If specified as an Object, the property names are used as the child Widget's
   * {@link Core.widget.Widget#config-ref} name, and the value is the child Widget/Widget config.
   *
   * When setting this, any items which are *only* in the outgoing child items which were created
   * by this container from raw config objects are destroyed.
   *
   * Usage patterns:
   *
   * ```javascript
   * myContainer.items = {
   *     name : {
   *         type  : 'textfield',
   *         label : 'User name'
   *     },
   *     age : {
   *         type  : 'numberfield',
   *         label : 'User age'
   *     }
   * };
   * ```
   *
   * or
   *
   * ```javascript
   * myContainer.items = [{
   *     ref   : 'name',
   *     type  : 'textfield',
   *     label : 'User name'
   * },
   *     ref   : 'age',
   *     type  : 'numberfield',
   *     label : 'User age'
   * }];
   * ```
   *
   * @property {Core.widget.Widget[]}
   * @accepts {Array<ContainerItemConfig|MenuItemConfig|Core.widget.Widget>|Object<String,ContainerItemConfig|MenuItemConfig>}
   */
  get items() {
    const me = this;
    me.getConfig("lazyItems");
    if (!me._items) {
      if (me.initializingItems) {
        return [];
      }
      me._items = new Bag();
      me._items.temporary = true;
    }
    return me._items.values;
  }
  processItemsArray(items2, result) {
    const len = items2.length;
    let i, item;
    for (i = 0; i < len; i++) {
      item = items2[i];
      if (item instanceof Widget) {
        item.parent = this;
        item.element.classList.remove(...Widget.outerCls);
      } else {
        item = this.createWidget(item);
      }
      if (item) {
        if (item.ref || item.id) {
          this.addDescendant(item);
        }
        result.push(item);
      }
    }
  }
  processItemsObject(items2, namedItems, result = []) {
    let item, ref;
    for (ref in items2) {
      item = items2[ref];
      if (item) {
        if (namedItems && ref in namedItems) {
          item = typeof item === "object" ? ObjectHelper.merge(ObjectHelper.clone(namedItems[ref]), item) : namedItems[ref];
        }
        if (item) {
          if (item instanceof Widget) {
            item.parent = this;
          } else {
            if (item instanceof Object) {
              item.ref = ref;
            }
            item = this.createWidget(item);
          }
          if (item) {
            item.ref = ref;
            this.addDescendant(item);
            result.push(item);
          }
        }
      }
    }
    return result;
  }
  onChildAdd(item) {
    const me = this;
    if (item.innerItem && me.readOnly && !item.ignoreParentReadOnly) {
      item.readOnly = true;
    }
    me.onChildAddLayout(item);
    if (item.ref || item.id) {
      for (let current = me; current; current = current.parent) {
        current.addDescendant(item);
      }
    }
    me.syncChildCount(true);
    !me.onConfigChange.$nullFn && me.onConfigChange({
      name: "items",
      config: me.$meta.configs.items
    });
  }
  onChildAddLayout(item) {
    if (item.innerItem) {
      this.layout.onChildAdd(item);
    }
  }
  onChildHide(hidden) {
    var _a5;
    super.onChildHide(hidden);
    if ((_a5 = this._items) == null ? void 0 : _a5.includes(hidden)) {
      this.syncChildCount(true);
    }
  }
  onChildShow(shown) {
    var _a5;
    super.onChildShow(shown);
    if ((_a5 = this._items) == null ? void 0 : _a5.includes(shown)) {
      this.syncChildCount(true);
    }
  }
  syncChildCount(enforceHideWhenEmpty) {
    if (!this.isConfiguring && !this.isDestroying) {
      const me = this, {
        // This must be our direct child item payload, not all items owned by this Container.
        // It's used to sync the b-first-visible-child/b-last-visible-child class presence.
        items: items2,
        hasItems
      } = me, visibleItems = items2.filter(isNotHidden), { length: visibleLength } = visibleItems;
      me.visibleChildCount = visibleLength;
      if (me.hideWhenEmpty && enforceHideWhenEmpty) {
        const shouldHide = Boolean(!visibleLength);
        if (Boolean(me._hidden) !== shouldHide) {
          me.hidden = shouldHide;
        }
      }
      items2.forEach((childItem) => childItem.element.classList.remove("b-last-visible-child", "b-first-visible-child"));
      if (visibleLength) {
        visibleItems[0].element.classList.add("b-first-visible-child");
        visibleItems[visibleLength - 1].element.classList.add("b-last-visible-child");
      }
      me.hasItems = Boolean(me.childItems.length);
      me.contentElement.classList[visibleLength ? "remove" : "add"]("b-no-visible-children");
      if (!me.isComposable) {
        me.updateTextContent(me._textContent);
      } else if (hasItems !== me.hasItems) {
        me.recompose();
      }
    }
  }
  syncFlexDirection() {
    const me = this, { contentElement, flexColCls, flexRowCls } = me, classList = new DomClassList(contentElement.className), styles2 = DomHelper.getStyleValue(contentElement, stylesToCheck);
    classList[flexRowCls] = classList[flexColCls] = 0;
    if (styles2.display === "flex") {
      classList[styles2["flex-direction"] === "row" ? flexRowCls : flexColCls] = 1;
    }
    DomHelper.syncClassList(contentElement, classList);
  }
  addDescendant(item) {
    const ref = item.ref || item.id, widgetMap = this._widgetMap || (this._widgetMap = {});
    if (!widgetMap[ref]) {
      widgetMap[ref] = item;
    }
  }
  cleanupWidgetMap(item) {
    const me = this, toRemove = /* @__PURE__ */ new Map(), ref = item.ref || item.id;
    if (ref) {
      toRemove.set(ref, item);
    }
    item.eachWidget((child) => {
      const ref2 = child.ref || child.id;
      if (ref2) {
        toRemove.set(ref2, child);
      }
    });
    for (let current = me; current; current = current.parent) {
      for (const [ref2, item2] of toRemove) {
        if (current.widgetMap[ref2] === item2) {
          delete current.widgetMap[ref2];
        }
      }
    }
  }
  onChildRemove(item) {
    const me = this;
    me.cleanupWidgetMap(item);
    me.layout.onChildRemove(item);
    me.syncChildCount(true);
    !me.onConfigChange.$nullFn && me.onConfigChange({
      name: "items",
      config: me.$meta.configs.items
    });
  }
  /**
   * An object which contains a map of descendant widgets keyed by their {@link Core.widget.Widget#config-ref}.
   * All descendant widgets will be available in the `widgetMap`.
   * @property {Object<String,Core.widget.Widget>}
   * @readonly
   * @category Widget hierarchy
   */
  get widgetMap() {
    if (!this._widgetMap) {
      this._widgetMap = {};
    }
    if (!this.initializingItems) {
      this.getConfig("items");
    }
    return this._widgetMap;
  }
  //region Record & values
  changeRecord(record) {
    this._record = record == null ? emptyObject7 : null;
    return record;
  }
  updateRecord(record) {
    var _a5;
    const me = this;
    (_a5 = me.recordUpdateDetacher) == null ? void 0 : _a5.call(me);
    me.trigger("beforeSetRecord", { record });
    me.clearError();
    me._hasChanges = false;
    me.setValues(record, {
      onlyName: me.strictRecordMapping,
      highlight: Boolean(me.$highlight)
    });
    if (me.autoUpdateFields && (record == null ? void 0 : record.firstStore)) {
      me.recordUpdateDetacher = record.firstStore.ion({
        update: me.onRecordUpdated,
        thisObj: me
      });
    }
  }
  setRecord(record, highlightChanges) {
    this.$highlight = highlightChanges;
    this.record = record;
    this.$highlight = false;
  }
  onRecordUpdated({ record }) {
    if (record === this.record) {
      this.setValues(this.record, true, true);
    }
  }
  // A method to override to affect making a decision on whether a field should be updated
  shouldUpdateRecordOnAction(record, event) {
    return record && (event.userAction || !this.autoUpdateUserChangesOnly);
  }
  /**
   * A function called by descendant widgets after they trigger their 'change' event, in reaction to field changes.
   * By default, implements the functionality for the `autoUpdateRecord` config.
   *
   * @param {Object} params Normally the event params used when triggering the 'change' event
   * @internal
   */
  onFieldChange(eventData) {
    const me = this, { source } = eventData, { hasChanges, isSettingValues, initialValues } = me;
    if (!isSettingValues) {
      if (!me.isConfiguring && me.autoUpdateRecord && me.shouldUpdateRecordOnAction(me.record, eventData)) {
        const { strictRecordMapping, record } = me, { name, ref, isValid = true, defaultBindProperty } = source, value2 = source[defaultBindProperty || "value"], key = strictRecordMapping ? name : name || ref;
        if (record && key && isValid) {
          if (record.isModel) {
            record.setValue(key, value2);
          } else {
            record[key] = value2;
          }
        }
      }
      if (initialValues) {
        const sourceName = source.getValueName(initialValues[optionsProp]);
        if (sourceName) {
          (me.changeSet || (me.changeSet = {}))[sourceName] = !ObjectHelper.isEqual(source.value, initialValues[sourceName]);
          me._hasChanges = Boolean(me.changeSet && Object.values(me.changeSet).some((v) => v));
        }
      }
      if (me.hasChanges !== hasChanges) {
        me.trigger("dirtyStateChange", { dirty: me.hasChanges });
      }
    }
  }
  /**
   * Returns `true` if any of the fields in this container have been changed since the container was
   * {@link #property-record loaded with a record}.
   *
   * Note that this value does not update during typing. The field's `change` event which is triggered when the
   * field is blurred causes this to be updated.
   *
   * For example when processing a "close" button click, this value will be in its correct state.
   *
   * @property {Boolean}
   */
  get hasChanges() {
    return Boolean(this._hasChanges);
  }
  getValues(filterFn) {
    const values = {};
    this.eachWidget((widget, control) => {
      if (widget.isolateFields) {
        control.down = false;
      } else if ("name" in widget && (!filterFn || filterFn(widget))) {
        values[widget.name] = widget.value;
      }
    }, true);
    return values;
  }
  /**
   * Retrieves or sets all values from/to contained widgets.
   *
   * The property set or read from a contained widget is its {@link Core.widget.Widget#config-defaultBindProperty}.
   *
   * This defaults to the `value` for fields.
   *
   * You may add child widgets which may accept and yield a value to/from another property, such as a `Button` having
   * its {@link Core.widget.Button#config-href} set.
   *
   * Accepts and returns a map, using {@link Core.widget.Field#config-name}, {@link Core.widget.Widget#config-ref} or
   * {@link Core.widget.Widget#config-id} (in that order) as keys.
   *
   * ```javascript
   * const container = new Container({
   *     appendTo : document.body,
   *     items    : {
   *         firstName : {
   *             type : 'textfield
   *         },
   *         surName : {
   *             type : 'textfield
   *         }
   *         saveButton : {
   *             type                : 'button',
   *             text                : 'Save',
   *             defaultBindProperty : 'href'
   *             href                : '#'
   *         }
   *     }
   * });
   *
   * container.values = {
   *     firstName  : 'Clark',
   *     surname    : 'Kent',
   *     saveButton : '#save-route'
   * };
   * ```
   *
   * @property {Object<String,Object>}
   */
  get values() {
    const values = {};
    this.gatherValue(values);
    return values;
  }
  set values(values) {
    this.assignValue(values);
  }
  /**
   * Returns `true` if currently setting values. Allow fields to change highlighting to distinguishing between
   * initially setting values and later on changing values.
   * @property {Boolean}
   */
  get isSettingValues() {
    return Boolean(this.assigningValues);
  }
  get assigningValues() {
    var _a5;
    return this._assigningValues || ((_a5 = this.parent) == null ? void 0 : _a5.assigningValues);
  }
  set assigningValues(v) {
    this._assigningValues = v;
  }
  assignValue(values, options) {
    super.assignValue(values, options);
    if (!this.isolateFields) {
      this.setValues(values, options);
    }
  }
  gatherValue(values) {
    super.gatherValue(values);
    if (!this.isolateFields) {
      this.gatherValues(values);
    }
  }
  setValues(values, options = this.assignValueDefaults) {
    const me = this;
    me.assigningValues = options;
    me.initialValues = {
      optionsProp: options
    };
    me.eachWidget((widget) => {
      const key = widget.getValueName(options);
      if (key && (!values || key in values)) {
        me.initialValues[key] = (values == null ? void 0 : values[key]) || null;
      }
      widget.assignValue(values, options);
    }, false);
    me.assigningValues = false;
  }
  //endregion
  get hasNoChildren() {
    const me = this, { items: items2, lazyItems } = me.initialConfig, itemsArray = items2 && (isArray2(items2) ? items2 : ObjectHelper.values(items2)), lazyItemsArray = lazyItems && (isArray2(lazyItems) ? lazyItems : ObjectHelper.values(lazyItems)), whichItems = me.isConfiguring ? lazyItemsArray || itemsArray : me.items;
    return !(whichItems == null ? void 0 : whichItems.filter(isNotHidden).length);
  }
  afterRecompose() {
    super.afterRecompose();
    this.realign();
  }
  updateTextContent(textContent) {
    const me = this;
    if (!me.isComposable) {
      const needsClass = Boolean(textContent && me.hasNoChildren), { classList } = me.contentElement, changed = needsClass !== classList.contains("b-text-content");
      if (changed) {
        classList[needsClass ? "add" : "remove"]("b-text-content");
        if (me.rendered) {
          me.realign();
        }
      }
    }
  }
  updateLayoutStyle(layoutStyle) {
    DomHelper.applyStyle(this.contentElement, layoutStyle);
  }
  updateElement(element) {
    super.updateElement(...arguments);
    if (element) {
      const { classList } = this.contentElement, { containerCls } = this.layout;
      classList.add("b-content-element");
      if (containerCls) {
        classList.add(containerCls);
      }
    }
  }
  onInternalPaint() {
    var _a5;
    (_a5 = super.onInternalPaint) == null ? void 0 : _a5.call(this, ...arguments);
    this.getConfig("contentElMutationObserver");
  }
  changeContentElMutationObserver(contentElMutationObserver, oldContentElMutationObserver) {
    if (oldContentElMutationObserver) {
      oldContentElMutationObserver.takeRecords();
      oldContentElMutationObserver.disconnect();
    }
    if (contentElMutationObserver) {
      const me = this, {
        element,
        contentElement
      } = me;
      contentElMutationObserver = new MutationObserver((mutations) => me.onContentElMutation(mutations));
      contentElMutationObserver.observe(contentElement, { attributes: true });
      if (contentElement !== element) {
        contentElMutationObserver.observe(element, { attributes: true });
      }
      me.syncFlexDirection();
    }
    return contentElMutationObserver;
  }
  onContentElMutation(mutations) {
    if (boxLayouts[this.layout.type] && !this.textContent && this.isVisible && mutations.some((m) => this.contentElement.contains(m.target))) {
      this.syncFlexDirection();
    }
  }
  changeLayout(config, existingLayout) {
    return Layout.reconfigure(existingLayout, config, {
      owner: this,
      defaults: {
        owner: this
      }
    });
  }
  // Items to iterate over
  get childItems() {
    return this.items;
  }
  get childItemsOrderedByRelevance() {
    return this.items;
  }
  /**
   * Returns a directly contained widget by id
   * @param {String} id The widget id
   * @returns {Core.widget.Widget}
   */
  getWidgetById(id2) {
    return this.widgetMap[id2];
  }
  /**
   * This function is called prior to creating widgets, override it in subclasses to allow containers to modify the
   * configuration of each widget. When adding a widget to a container hierarchy each parent containers
   * `processWidgetConfig` will be called. Returning false from the function prevents the widget from being added at
   * all.
   */
  processWidgetConfig(widget) {
  }
  /**
   * This method combines container {@link #config-defaults}
   * @param {String|ContainerItemConfig} widgetConfig
   * @param {String|Function} [type] The type of widget described by `widgetConfig`.
   * @returns {ContainerItemConfig}
   * @internal
   */
  setupWidgetConfig(widgetConfig, type) {
    const me = this;
    if (typeof widgetConfig === "string") {
      widgetConfig = {
        html: widgetConfig
      };
    } else if (widgetConfig.nodeType === Element.ELEMENT_NODE) {
      widgetConfig = {
        element: widgetConfig,
        id: widgetConfig.id
      };
    }
    if (typeof type === "string" || !type && (type = widgetConfig.type)) {
      if (type === "checkbox" && BrowserHelper.isMobile && Widget.resolveType("slidetoggle", true)) {
        type = widgetConfig.type = "slidetoggle";
      }
      type = Widget.resolveType(type, true);
    }
    widgetConfig = (type || Widget).mergeConfigs(me.defaults, widgetConfig, { parent: me });
    for (let ancestor = widgetConfig.parent; ancestor; ancestor = ancestor.parent) {
      if (ancestor.processWidgetConfig(widgetConfig) === false) {
        return null;
      }
    }
    if (me.trigger("beforeWidgetCreate", { widgetConfig }) === false) {
      return null;
    }
    return widgetConfig;
  }
  /**
   * This function converts a Widget config object into a Widget.
   * @param {ContainerItemConfig} widget A Widget config object.
   * @internal
   */
  createWidget(widget) {
    const result = Widget.create(this.setupWidgetConfig(widget), this.defaultType);
    result && (result._createdBy = this);
    return result;
  }
  // Reapply defaults, not used during config
  updateDefaults(defaults, oldDefaults) {
    if (!this.isConfiguring && defaults) {
      const entries = Object.entries(defaults);
      this.eachWidget((widget) => {
        entries.forEach(([prop, value2]) => {
          if (!oldDefaults || widget[prop] === oldDefaults[prop]) {
            widget[prop] = value2;
          }
        });
      }, false);
    }
  }
  render() {
    this.getConfig("lazyItems");
    this.layout.renderChildren();
    this.syncChildCount();
    super.render(...arguments);
  }
  get focusElement() {
    const { defaultFocus } = this;
    if (typeof defaultFocus === "string") {
      return this.element.querySelector(defaultFocus);
    }
    let firstFocusable = defaultFocus && this.query(defaultFocus);
    if (!defaultFocus || !firstFocusable) {
      firstFocusable = this.query(isFocusable, true);
    }
    if (firstFocusable) {
      return firstFocusable.focusElement;
    }
    return super.focusElement;
  }
  doDestroy() {
    var _a5;
    (_a5 = this._items) == null ? void 0 : _a5.forEach((widget) => {
      var _a6;
      return (_a6 = widget.destroy) == null ? void 0 : _a6.call(widget);
    });
    this.layout.destroy();
    super.doDestroy();
  }
  /**
   * Returns `true` if all contained fields are valid, otherwise `false`
   * @property {Boolean}
   */
  get isValid() {
    let valid = true;
    this.eachWidget((widget) => {
      if ("isValid" in widget && widget.isVisibleForValidation && !widget.isValid) {
        valid = false;
      }
    }, true);
    return valid;
  }
  clearError() {
    this.eachWidget((widget) => {
      var _a5;
      return (_a5 = widget.clearError) == null ? void 0 : _a5.call(widget);
    });
  }
};
__publicField(Container, "$name", "Container");
__publicField(Container, "type", "container");
Container.initClass();
Container._$name = "Container";

// ../Core/lib/Core/util/Animator.js
var { assign } = ObjectHelper;
var now = () => performance.now();
var nostart = { start: false };
var unitRe = /^[.\d]+([^\d].*)?$/;
var getTransitions = (element) => {
  const $bryntum = element.$bryntum || (element.$bryntum = {});
  return $bryntum.transitions || ($bryntum.transitions = /* @__PURE__ */ Object.create(null));
};
var milliseconds = (duration, unit) => {
  var _a5;
  if (typeof duration === "string") {
    unit = (_a5 = unitRe.exec(duration)) == null ? void 0 : _a5[1];
    duration = parseFloat(duration);
  }
  return duration && duration * (unit === "s" || !unit && duration < 10 ? 1e3 : 1);
};
var syncTransitions = (element) => {
  const all = ObjectHelper.values(getTransitions(element)).filter((a) => a.completed == null).map((a) => a.toString());
  element.style.transition = all.join(", ");
};
var Anim = (Target) => class Anim extends Target {
  static get prototypeProperties() {
    return {
      _delay: null,
      _duration: null,
      _retain: null,
      _timing: null,
      _unit: null,
      owner: null,
      reverting: null
    };
  }
  constructor(...args) {
    super(...args);
    this.id = ++idSeed;
  }
  start() {
    this.startTime = now();
  }
  get delay() {
    var _a5, _b2, _c2;
    return milliseconds((_c2 = (_b2 = this._delay) != null ? _b2 : (_a5 = this.owner) == null ? void 0 : _a5.delay) != null ? _c2 : 0, this.unit);
  }
  set delay(v) {
    this._delay = v;
  }
  get duration() {
    var _a5;
    const { owner, unit } = this;
    return milliseconds((_a5 = this._duration) != null ? _a5 : owner ? owner.duration : Animator.defaultDuration, unit);
  }
  set duration(v) {
    this._duration = v;
  }
  get elapsed() {
    return now() - this.startTime;
  }
  get remaining() {
    return this.duration - this.elapsed;
  }
  get retain() {
    var _a5, _b2;
    return (_b2 = this._retain) != null ? _b2 : (_a5 = this.owner) == null ? void 0 : _a5.retain;
  }
  set retain(v) {
    this._retain = v;
  }
  get timing() {
    var _a5, _b2;
    return (_b2 = this._timing) != null ? _b2 : (_a5 = this.owner) == null ? void 0 : _a5.timing;
  }
  set timing(v) {
    this._timing = v;
  }
  get unit() {
    var _a5, _b2;
    return (_b2 = this._unit) != null ? _b2 : (_a5 = this.owner) == null ? void 0 : _a5.unit;
  }
  set unit(v) {
    this._unit = v;
  }
};
var idSeed = 0;
var _AnimatorTransition = class _AnimatorTransition extends Base.mixin(Anim) {
  static get prototypeProperties() {
    return {
      element: null,
      property: null,
      from: null,
      to: null,
      completed: null,
      promissory: null,
      reverting: null
    };
  }
  afterConstruct() {
    super.afterConstruct();
    const me = this, { element, transitions } = me;
    let { property } = me;
    [property, me.to] = DomHelper.unitize(property, me.to);
    me.from = DomHelper.unitize(property, me.from)[1];
    me.promissory = new Promissory();
    me.property = property;
    const was = transitions[property];
    transitions[property] = me;
    let { from } = me;
    if (was) {
      me.from = was.to;
      from = null;
      was.destroy();
    }
    if (from === null) {
      from = me.getCurrentStyleValue();
      if (!was) {
        me.from = from;
      }
    }
    element.style[property] = from;
    me.getCurrentStyleValue();
  }
  doDestroy() {
    const me = this;
    me.finish(false);
    if (me.completed && !me.retain) {
      me.clearStyle();
    }
    super.doDestroy();
  }
  get promise() {
    var _a5;
    return (_a5 = this.promissory) == null ? void 0 : _a5.promise;
  }
  get transitions() {
    return getTransitions(this.element);
  }
  clearStyle() {
    this.setStyle("");
  }
  finish(complete) {
    const me = this, { transitions, promissory, property } = me;
    if (promissory) {
      me.completed = complete;
      me.promissory = null;
      promissory.resolve(complete);
      if (transitions[property] === me) {
        delete transitions[property];
        if (!complete) {
          syncTransitions(me.element);
          me.clearStyle();
        }
      } else {
        me.completed = false;
      }
    }
  }
  getCurrentStyleValue() {
    return DomHelper.getStyleValue(this.element, this.property);
  }
  revert() {
    const { duration, elapsed, element, from, property, _retain: retain, _timing: timing } = this;
    return new _AnimatorTransition({
      element,
      property,
      retain,
      timing,
      duration: Math.round(Math.min(duration, elapsed)),
      reverting: this,
      to: from,
      unit: "ms"
    });
  }
  setStyle(value2) {
    this.element.style[this.property] = value2;
  }
  start() {
    const me = this, { delay: delay2, duration, element, property } = me;
    EventHelper.onTransitionEnd({
      element,
      property,
      duration: delay2 + duration + 20,
      thisObj: me.owner,
      handler: () => {
        var _a5;
        return (_a5 = me.finish) == null ? void 0 : _a5.call(me, true);
      }
      // finish() is gone if destroyed
    });
    super.start();
    me.setStyle(me.to);
  }
  toString() {
    const { delay: delay2, duration, property, timing } = this;
    return `${property} ${duration}ms ${timing || "ease-in-out"}${delay2 ? ` ${delay2}ms` : ""}`;
  }
};
__publicField(_AnimatorTransition, "$name", "AnimatorTransition");
var AnimatorTransition = _AnimatorTransition;
AnimatorTransition.initClass();
var _Animator = class _Animator extends Base.mixin(Anim) {
  static get prototypeProperties() {
    return {
      /**
       * The optional delay before starting the animation. Numbers less than 10 are assumed to be seconds
       * (instead of milliseconds) unless the `unit` property is specified.
       * @config {Number|String} delay
       */
      /**
       * The duration of the animation. Numbers less than 10 are assumed to be seconds (instead of milliseconds)
       * unless the `unit` property is specified.
       * @config {Number|String} duration
       * @default
       */
      /**
       * The element to animate.
       * @config {HTMLElement} element
       */
      element: null,
      /**
       * A callback function called when the animation completes. This is called after restoring styles to the
       * original values (based on {@link #config-retain}). When this function is provided, `retain` defaults to
       * `false`. By implementing this function, a CSS class can be applied to the {@link #config-element} to
       * give the proper style, while the inline styles are removed (e.g., a hide animation based on opacity).
       *
       * For example:
       * ```javascript
       *  const anim = Animator.run({
       *      element,
       *      duration : 500,
       *      opacity  : 0,
       *
       *      finalize() {
       *          element.classList.add('hidden');
       *      }
       *  });
       *
       *  await anim.done();
       * ```
       *
       * @config {Function} finalize
       * @returns {void}
       */
      finalize: null,
      /**
       * A callback function called when the animation completes. This is called prior to restoring styles to the
       * original values (based on {@link #config-retain}).
       *
       * @config {Function} prefinalize
       * @returns {void}
       * @internal
       */
      prefinalize: null,
      /**
       * Set to `true` to retain the style property values after the animation. This defaults to `true` if a
       * {@link #config-finalize} function is not specified, and `false` otherwise. When a `finalize` function
       * is provided, it is typically to apply a CSS class to achieve the desired styling so that inline styles
       * can be removed.
       * @config {Boolean} retain
       */
      /**
       * The [timing function](https://developer.mozilla.org/en-US/docs/Web/CSS/transition-timing-function) for
       * the animation.
       * @config {String} timing
       * @default 'ease-in-out'
       */
      /**
       * The duration/delay unit (either `'s'` or `'ms'`).
       * @config {'s'|'ms'} unit
       */
      /**
       * This readonly property is set to `true` when the animation completes or `false` if the animation is
       * aborted (by calling the `destroy()` method).
       * @member {Boolean} completed
       * @readonly
       */
      completed: null,
      /**
       * An array containing a mixture of `Animator` and/or `AnimatorTransition` objects, depending on what was
       * specified at construction time.
       * @member {Core.util.Animator[]|AnimatorTransition[]} items
       * @readonly
       */
      /**
       * When passed at construction time, `items` can be an array of other `Animator` config objects. This can be
       * used to animate multiple elements and wait for this instance to be {@link #function-done done}.
       * @config {Core.util.Animator[]} items
       */
      items: null
    };
  }
  static register(name, fn2) {
    if (ObjectHelper.isObject(name)) {
      ObjectHelper.entries(name).forEach((entry) => _Animator.register(...entry));
      return;
    }
    _Animator.fx[name] = fn2;
    _Animator[name] = (options) => {
      if (DomHelper.isElement(options)) {
        options = {
          element: options,
          [name]: {}
        };
      } else {
        options = {
          element: options.element,
          [name]: options
        };
        delete options[name].element;
      }
      return _Animator.run(options);
    };
  }
  /**
   * A short-hand way to create an `Animator` instance and call its {@link #function-start} method.
   *
   * ```javascript
   *  const anim = Animator.run({
   *      element,
   *      duration : 500,
   *
   *      // style transitions:
   *      opacity : 0
   *  });
   *
   *  await anim.done();
   * ```
   * @param {Core.util.Animator|AnimatorConfig} options A config object for an `Animator` instance.
   * @returns {Core.util.Animator}
   */
  static run(options) {
    return new _Animator(options).start();
  }
  constructor(options) {
    var _a5;
    super(null);
    const me = this, items2 = [], properties = {};
    let anim, fx, key, t;
    if (Array.isArray(options)) {
      me.items = options;
    } else {
      for (key in options) {
        (_Animator.specialPropsRe.test(key) ? me : properties)[key] = options[key];
      }
    }
    ObjectHelper.keys(properties).forEach((property) => {
      t = properties[property];
      if (t != null && t !== false && t === t) {
        if (!(fx = _Animator.fx[property])) {
          t = assign(me.defaults, typeof t === "object" ? t : { to: t });
          t.owner = me;
          t.property = property;
          anim = new AnimatorTransition(t);
        } else {
          t = assign(me.defaults, fx(t, me, property));
          t.owner = me;
          anim = new _Animator(t);
        }
        items2.push(anim);
      }
    });
    (_a5 = me.items) == null ? void 0 : _a5.forEach((item) => {
      if (ObjectHelper.isInstantiated(item)) {
        item.owner = me;
      } else {
        item = assign(me.defaults, item);
        item.owner = me;
        item = new _Animator(item);
      }
      items2.push(item);
    });
    me.items = items2;
    me.promise = (items2.length === 1 ? items2[0].promise : Promise.all(items2.map((it) => it.promise))).then((res) => {
      var _a6;
      (_a6 = me.finish) == null ? void 0 : _a6.call(me, res);
      return me.completed || false;
    });
  }
  doDestroy() {
    this.items.forEach((a) => a.destroy());
    super.doDestroy();
  }
  get defaults() {
    return {
      element: this.element
    };
  }
  get retain() {
    const { _retain, finalize, owner } = this;
    return _retain != null ? _retain : finalize ? false : owner ? owner.retain : true;
  }
  set retain(v) {
    super.retain = v;
  }
  /**
   * Returns a `Promise` that resolves to a `Boolean` when this animation completes. The resolved value is that of
   * this instance's {@link #property-completed} property.
   * @async
   */
  done() {
    return this.promise;
  }
  finish(complete) {
    var _a5, _b2;
    const me = this, { items: items2 } = me;
    syncTransitions(me.element);
    me.completed = typeof complete === "boolean" ? complete : !complete.some((a) => !a);
    (_a5 = me.prefinalize) == null ? void 0 : _a5.call(me, me.completed, me);
    while (items2.length) {
      items2.pop().destroy();
    }
    (_b2 = me.finalize) == null ? void 0 : _b2.call(me, me.completed, me);
  }
  revert(options) {
    var _a5;
    const me = this, { reverting } = me, start = !options || ((_a5 = options.start) != null ? _a5 : true);
    let anim = me.defaults;
    if (reverting) {
      if (reverting.finalize) {
        anim.finalize = reverting.finalize;
      }
      if (reverting.prefinalize) {
        anim.prefinalize = reverting.prefinalize;
      }
      anim.retain = reverting._retain;
    }
    anim = assign(anim, options, {
      items: me.items.map((it) => it.revert(nostart)),
      reverting: me
    });
    anim = new _Animator(anim);
    start && anim.start();
    return anim;
  }
  /**
   * Starts this animation and returns a reference to itself. This method is called automatically by the
   * {@link #function-run-static} method.
   * @returns {Core.util.Animator}
   */
  start() {
    const { element, items: items2 } = this;
    super.start();
    if (items2.length) {
      syncTransitions(element);
      items2.forEach((a) => a.start());
    }
    return this;
  }
};
__publicField(_Animator, "$name", "Animator");
var Animator = _Animator;
Animator.initClass().Transition = AnimatorTransition;
Animator.specialPropsRe = new RegExp(`^(?:${Object.keys(Animator.prototypeProperties).concat(
  Object.keys(Animator.superclass.prototypeProperties)
).map((s) => s[0] === "_" ? s.substr(1) : s).join("|")})$`);
Animator.defaultDuration = 200;
Animator.fx = {};
Animator.register({
  /**
   * A compound animation to achieve `transform: scale()` and `opacity: 0`. The `scale` defaults to 8 but can be set
   * in the `anim` config object.
   *
   * For example
   * ```javascript
   *  const puff = Animator.puff(element);
   *
   *  const puff = Animator.puff({
   *      element,
   *      scale : 12
   *  });
   * ```
   *
   * This compound animation can also be specified in an `Animator` config object along with other style transitions:
   * ```javascript
   *  const anim = Animator.run({
   *      element,
   *      marginLeft : -200,
   *      puff       : true   // true for default scale, a number, or config object
   *  });
   * ```
   * @param {Element|AnimatorConfig|Core.util.Animator|Object} anim The element to animate or the config object containing at least
   * the `element` property. This config object can contain an optional `scale` property to adjust the animation's
   * `transform: scale()` value.
   * @param {Number} [anim.scale=8] The scale value for the `transform:scale()` transition.
   * @returns {Core.util.Animator}
   * @static
   */
  puff(anim) {
    if (anim === true) {
      anim = {};
    } else if (typeof anim !== "object") {
      anim = {
        transform: `scale(${anim})`
      };
    } else if (anim.scale) {
      anim = ObjectHelper.clone(anim);
      anim.transform = `scale(${anim.scale})`;
      delete anim.scale;
    }
    return assign({
      opacity: 0,
      transform: "scale(8)"
    }, anim);
  }
});
Animator._$name = "Animator";

// ../Core/lib/Core/util/ClickRepeater.js
var ClickRepeater = class extends Base.mixin(Delayable_default) {
  static get configurable() {
    return {
      /**
       * The element on which to fire autorepeating `click` events when the mouse is held down.
       * @config {HTMLElement}
       */
      element: null,
      /**
       * A query selector which specifies subtargets of this ClickRepeater's {@link #config-element}
       * which act as the click auto repeat event targets.
       * @config {String}
       * @default
       */
      delegate: null,
      /**
       * How long in milliSeconds to pause before starting the click repeats.
       * @config {Number}
       * @default
       */
      delay: 500,
      /**
       * Clicks per second to start firing after the initial {@link #config-delay}
       * @config {Number}
       * @default
       */
      startRate: 2,
      /**
       * Clicks per second to fire at top speed, after accelerating over the {@link #config-accelerateDuration}
       * @config {Number}
       * @default
       */
      endRate: 20,
      /**
       * How long in milliseconds to accelerate from the
       * {@link #config-startRate} to the {@link #config-startRate}.
       * @config {Number}
       * @default
       */
      accelerateDuration: 4e3,
      pressedCls: "b-pressed"
    };
  }
  doDestroy() {
    var _a5;
    (_a5 = this.mousedownRemover) == null ? void 0 : _a5.call(this);
    this.cancel();
    super.doDestroy();
  }
  cancel() {
    var _a5, _b2, _c2;
    const me = this;
    (_a5 = me.activeElement) == null ? void 0 : _a5.classList.remove(me.pressedCls);
    me.activeElement = null;
    (_b2 = me.acceleration) == null ? void 0 : _b2.cancel();
    me.activeListenerRemover = (_c2 = me.activeListenerRemover) == null ? void 0 : _c2.call(me);
    me.clearTimeout(me.autoRepeatTimer);
    me.clearTimeout(me.repeatTimer);
  }
  updateElement(element) {
    var _a5;
    (_a5 = this.mousedownRemover) == null ? void 0 : _a5.call(this);
    this.mousedownRemover = EventHelper.on({
      element,
      mousedown: "onMouseDown",
      thisObj: this
    });
  }
  onMouseDown(e) {
    const me = this, target = me.delegate ? e.target.closest(me.delegate) : me.element;
    me.cancel();
    if (target) {
      me.activeElement = target;
      me.triggerEvent = e;
      target.classList.add(me.pressedCls);
      me.activeListenerRemover = EventHelper.on({
        mouseup: {
          element: document,
          capture: true,
          handler: "onMouseUp"
        },
        mousemove: {
          element: target,
          handler: "onTargetMouseMove"
        },
        mouseleave: {
          element: target,
          handler: "onTargetMouseLeave"
        },
        mouseenter: {
          element: target,
          handler: "onTargetMouseEnter"
        },
        thisObj: me
      });
      me.autoRepeatTimer = me.setTimeout("startAutoRepeat", me.delay);
    }
  }
  onMouseUp() {
    this.cancel();
  }
  onTargetMouseLeave() {
    this.activeElement.classList.remove(this.pressedCls);
    this.clearTimeout(this.repeatTimer);
  }
  onTargetMouseEnter(e) {
    const me = this;
    if (!e.buttons) {
      me.cancel();
    } else {
      me.activeElement.classList.add(me.pressedCls);
      me.triggerEvent = e;
      me.fireClick();
    }
  }
  onTargetMouseMove(e) {
    this.triggerEvent = e;
  }
  startAutoRepeat() {
    const me = this;
    me.interval = me.startInterval = 1e3 / me.startRate;
    me.accelerationDelta = me.startInterval - 1e3 / me.endRate;
    me.fireClick();
    me.acceleration = FunctionHelper.animate(me.accelerateDuration, me.nextTick, me, "easeOutQuad");
  }
  nextTick(progress) {
    this.interval = this.startInterval - this.accelerationDelta * progress;
  }
  fireClick() {
    const me = this, clickEvent = new MouseEvent("click", me.triggerEvent);
    me.triggerEvent.target.dispatchEvent(clickEvent);
    me.repeatTimer = me.setTimeout(me.fireClick, me.interval);
  }
};
ClickRepeater._$name = "ClickRepeater";

// ../Core/lib/Core/widget/mixin/Rotatable.js
var autoRotateRe = /^(?:undefined|null|LEFT|RIGHT)$/;
var inverted = {
  TOP: "BOTTOM",
  RIGHT: "LEFT",
  BOTTOM: "TOP",
  LEFT: "RIGHT"
};
var canonicalDock = (dock) => {
  const DOCK = dock == null ? void 0 : dock.toUpperCase();
  return [DOCK, DOCK === "LEFT" || DOCK === "RIGHT"];
};
var Rotatable_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    static get configurable() {
      return {
        /**
         * Set to `'left'` to rotate the button content 90 degrees counter-clockwise or `'right'` for clockwise.
         * @member {'left'|'right'} rotate
         */
        /**
         * Specify `'left'` to rotate the button content 90 degrees counter-clockwise or `'right'` for clockwise.
         * @config {'left'|'right'}
         */
        rotate: null,
        invertRotate: null
      };
    }
    compose() {
      const { rotate } = this;
      return {
        class: {
          [`b-rotate-${(rotate || "").toLowerCase()}`]: rotate,
          "b-rotate-vertical": rotate
        }
      };
    }
    syncRotationToDock(dock) {
      if (autoRotateRe.test(String(this.rotate))) {
        const [DOCK, vertical] = canonicalDock(dock);
        this.rotate = vertical ? this.invertRotate ? inverted[DOCK] : DOCK : null;
      }
    }
    get widgetClass() {
      return null;
    }
  }, __publicField(_a5, "$name", "Rotatable"), _a5;
};

// ../Core/lib/Core/widget/Tool.js
var Tool = class extends Widget.mixin(Rotatable_default) {
  compose() {
    const { align, href } = this, result = {
      tag: href != null ? "a" : "button",
      class: {
        [`b-align-${align || "end"}`]: 1,
        "b-icon": 1
      },
      // eslint-disable-next-line bryntum/no-listeners-in-lib
      listeners: {
        click: "onInternalClick"
      }
    };
    if (!href) {
      result.type = "button";
    }
    return result;
  }
  get focusElement() {
    return this.element;
  }
  get panel() {
    var _a5, _b2;
    if (((_a5 = this.parent) == null ? void 0 : _a5.isPanel) && ((_b2 = this.element) == null ? void 0 : _b2.parentNode.matches(".b-panel-header"))) {
      return this.parent;
    }
  }
  changeAlign(align) {
    return align;
  }
  onInternalClick(domEvent) {
    const me = this, { handler, panel } = me, bryntumEvent = { domEvent, tool: me };
    if (me.disabled) {
      return;
    }
    me.trigger("click", bryntumEvent);
    if (!me.isDestroyed) {
      me.trigger("action", bryntumEvent);
      if (!me.isDestroyed && (panel == null ? void 0 : panel.trigger("toolClick", bryntumEvent)) !== false) {
        handler && me.callback(handler, panel, [domEvent, panel, me]);
      }
    }
  }
  onInternalKeyDown(keyEvent) {
    const keyName = keyEvent.key.trim() || keyEvent.code;
    if (keyName === "Enter") {
      keyEvent.stopPropagation();
    }
  }
  updateDisabled(disabled, was) {
    var _a5;
    super.updateDisabled(disabled, was);
    disabled && ((_a5 = this.repeat) == null ? void 0 : _a5.cancel());
  }
  changeRepeat(repeat, oldRepeat) {
    oldRepeat == null ? void 0 : oldRepeat.destroy();
    return repeat && ClickRepeater.new({
      element: this.element
    }, repeat);
  }
  changeTooltip(tooltip, oldTooltip) {
    const me = this, stringTooltip = (tooltip == null ? void 0 : tooltip.html) || tooltip;
    if (!me.ariaLabel && !me.text && typeof stringTooltip === "string") {
      me.ariaLabel = me.optionalL(stringTooltip, me);
    }
    return super.changeTooltip(tooltip, oldTooltip);
  }
};
__publicField(Tool, "$name", "Tool");
__publicField(Tool, "type", "tool");
__publicField(Tool, "configurable", {
  /**
   * Specify `'start'` to place the tool before the owner's central element (e.g., the `title` of the panel).
   * @config {'start'|'end'}
   * @default 'end'
   * @category Float & align
   */
  align: {
    value: null,
    $config: {
      merge: "replace"
    }
  },
  /**
   * If provided, turns the tool into a link
   * @config {String}
   */
  href: null,
  /**
   * The function to call when this tool is clicked. May be a function or function name
   * prepended by `"up."` that is resolvable in an ancestor component (such as an owning
   * Grid, Scheduler, Calendar, Gantt or TaskBoard)
   *
   * @config {Function|String} handler
   * @param {Event} handler.event The DOM event which activated the tool
   * @param {Core.widget.Panel} handler.panel The owning Panel of the tool
   * @param {Core.widget.Tool} handler.tool The clicked Tool
   * @returns {void}
   */
  /**
   * A {@link Core.util.ClickRepeater } config object to specify how click-and-hold gestures repeat the click
   * action.
   * @config {ClickRepeaterConfig}
   */
  repeat: null,
  defaultBindProperty: null,
  role: ""
});
Tool.initClass();
Tool._$name = "Tool";

// ../Core/lib/Core/widget/panel/CollapseTool.js
var rightRe = /right/i;
var autoAlign = (tool) => (tool.collapsed || tool.collapsing) && // special case to align the expand tool with the collapse tool
tool.owner.expandedHeaderDock === "top" && tool.owner.collapsible.direction.match(rightRe) ? "start" : "end";
var CollapseTool = class extends Tool {
  static get configurable() {
    return {
      /**
       * Set to `false` to disable automatic adjustment of the {@link #config-align} config based on the state
       * of the panel's {@link Core.widget.Panel#config-collapsed} config and the
       * {@link Core.widget.panel.PanelCollapser#config-direction}.
       *
       * If this is set to a function, that function is called passing the owning `Panel` instance and its
       * return value is assigned to the {@link #config-align} config.
       *
       * @config {Boolean|Function}
       * @param {Core.widget.Panel} owner The owning `Panel` instance
       * @returns {Boolean} Auto align value
       * @default
       */
      autoAlign: true,
      collapsed: null,
      collapsing: null,
      collapsify: false,
      // ...unaffected when the panel is collapsed
      direction: "up",
      // Our own setValues/getValues system should not set/get HTML content
      defaultBindProperty: null
    };
  }
  compose() {
    const { collapsed, direction } = this;
    return {
      "aria-label": this.L(`L{PanelCollapser.${collapsed ? "Expand" : "Collapse"}}`),
      class: {
        [`b-icon-collapse-${direction}`]: 1,
        "b-collapsed": collapsed
      }
    };
  }
  changeAutoAlign(v) {
    return v === true ? autoAlign : v;
  }
  syncAutoAlign() {
    const { autoAlign: autoAlign2 } = this;
    if (autoAlign2) {
      this.align = autoAlign2(this);
    }
  }
  updateAutoAlign() {
    this.syncAutoAlign();
  }
  updateCollapsed() {
    this.syncAutoAlign();
  }
  updateCollapsing() {
    this.syncAutoAlign();
  }
  updateDirection() {
    this.syncAutoAlign();
  }
};
__publicField(CollapseTool, "$name", "CollapseTool");
__publicField(CollapseTool, "type", "collapsetool");
CollapseTool.initClass();
CollapseTool._$name = "CollapseTool";

// ../Core/lib/Core/widget/panel/PanelCollapser.js
var defaultedDirectionRe = /^(?:UP|DOWN|LEFT|RIGHT)$/;
var dockBeforeRe = /^(left|top)$/i;
var emptyObject8 = {};
var headerDockRe = /^b-dock-(top|left|right|bottom)$/;
var revealerCls = "b-panel-collapse-revealer";
var sideDockRe = /^(?:left|right)$/i;
var unflexCls = "b-collapse-unflex";
var { round } = Math;
var canonicalDirection = ["up", "down", "left", "right"].reduce((o, v) => {
  o[v.toUpperCase()] = o[v] = v;
  return o;
}, {});
var clipByDock = {
  top: ["top", "bottom"],
  right: ["right", "left"],
  bottom: ["bottom", "top"],
  left: ["left", "right"]
};
var collapseDirectionByPlacement = {
  hl: "LEFT",
  hr: "RIGHT",
  vb: "DOWN",
  vt: "UP"
};
var crossAxis = {
  h: "w",
  w: "h"
};
var dockByDirection = {
  up: "top",
  right: "right",
  down: "bottom",
  left: "left"
};
var dockIsHorz = {
  top: false,
  right: true,
  bottom: false,
  left: true
};
var directionByDock = {
  top: "UP",
  right: "RIGHT",
  bottom: "DOWN",
  left: "LEFT"
};
var transverseTransform = {
  top: (rect) => `translate(0, -${round(rect.height || 0)}px)`,
  bottom: (rect) => `translate(0, ${round(rect.height || 0)}px)`,
  right: (rect) => `translate(${round(rect.width || 0)}px, 0)`,
  left: (rect) => `translate(-${round(rect.width || 0)}px, 0)`
};
var idSeed2 = 0;
var PanelCollapser = class extends Base.mixin(Factoryable_default) {
  static get configurable() {
    return {
      /**
       * An animation config object.
       * @config {Object} animation
       * @property {Number} [animation.duration=200] The duration of the animation (in milliseconds).
       */
      animation: {
        duration: 200
      },
      /**
       * Tracks whether or not the panel is collapsed.
       * @config {Boolean}
       * @private
       */
      collapsed: {
        value: null,
        $config: null,
        default: false
      },
      /**
       * Specifies the direction of panel collapse. The default value for this config is determined dynamically
       * based on the {@link Core.widget.Panel#config-header header's} `dock` property and the containing layout's
       * flex direction and, therefore, often does not need to be explicitly specified.
       *
       * This config can be any of the following:
       * - `'up'`
       * - `'down'`
       * - `'left'`
       * - `'right'`
       *
       * The default `direction` property is inferred from the position of the Panel in a flexbox layout.
       *
       * If the Panel is the last child of a flexbox container, the `direction` is `'right'` for a horizontal
       * flexbox layout and `'down'` for a vertical layout.
       *
       * All other Panels in a flexbox container have the `direction` set to `'left'` for a horizontal layout and
       * `'up'` for a vertical layout.
       *
       * @config {'up'|'down'|'left'|'right'}
       */
      direction: null,
      /**
       * The tooltip to use for the collapse tool when the panel is expanded.
       * @config {String}
       */
      collapseTooltip: "L{Collapse}",
      /**
       * The tooltip to use for the expand tool when the panel is collapsed.
       * @config {String}
       */
      expandTooltip: "L{Expand}",
      panel: {
        value: null,
        $config: "nullify"
      },
      /**
       * To support the panel's collapsed size, a minimum width and height may be assigned to the panel's header,
       * based on this config and the panel's positioning style.
       *
       * When a panel is collapsed it may need to retain the pre-collapse dimension perpendicular to the collapse
       * {@link #config-direction}. For example, the height of a panel that collapses to the left. The dimension
       * parallel to the collapse (the width in this example) may also need to be supported using the pre-collapse
       * size of the panel's header.
       *
       * When this config is set to `true`, or by default when the owning panel is `position: absolute`, both
       * axes are given a minimum size based on the panel's pre-collapse size. When this config is `false`, no
       * minimum sizes will be assigned.
       *
       * This config can also be a string containing the single letters 'w' and/or 'h' indicating which axis/axes
       * of the panel header should be assigned a minimum size. That is, 'w' to assign only a minimum width, 'h'
       * for only a minimum height, or 'wh' to assign both.
       *
       * @config {String|Boolean}
       * @internal
       */
      supportAxis: null,
      /**
       * The collapse/expand tool. The `type` of this instance should not be changed but the tool instance can be
       * configured in other ways via this config property.
       * @config {ToolConfig|Core.widget.Tool|null}
       */
      tool: {
        type: "collapsetool",
        handler(ev) {
          var _a5;
          (_a5 = this.collapsible) == null ? void 0 : _a5.onCollapseClick(ev);
        }
      }
    };
  }
  static get factoryable() {
    return {
      defaultType: "inline"
    };
  }
  get collapsing() {
    return this.collapsingExpanding === "collapsing";
  }
  get collapsingExpanding() {
    var _a5;
    const state = (_a5 = this.currentOperation) == null ? void 0 : _a5.collapsing;
    return state == null ? null : state ? "collapsing" : "expanding";
  }
  get currentDock() {
    var _a5, _b2, _c2, _d;
    return (_d = (_c2 = (_b2 = (_a5 = this.panel) == null ? void 0 : _a5.header) == null ? void 0 : _b2.dock) == null ? void 0 : _c2.toLowerCase()) != null ? _d : "top";
  }
  get expanding() {
    return this.collapsingExpanding === "expanding";
  }
  get collapseTool() {
    var _a5, _b2;
    return (_b2 = (_a5 = this.panel) == null ? void 0 : _a5.tools) == null ? void 0 : _b2.collapse;
  }
  get collapseDim() {
    return sideDockRe.test(this.collapseDir) ? "width" : "height";
  }
  getCollapseDir(canonical) {
    var _a5;
    let { direction, panel } = this;
    if (!direction || defaultedDirectionRe.test(direction)) {
      const placement = panel == null ? void 0 : panel.placement;
      if (placement) {
        direction = collapseDirectionByPlacement[placement];
      } else {
        direction = directionByDock[((_a5 = panel == null ? void 0 : panel.header) == null ? void 0 : _a5.dock) || "top"];
      }
    }
    return canonical ? canonicalDirection[direction] : direction;
  }
  get collapseDir() {
    return this.getCollapseDir(true);
  }
  get collapseDock() {
    return this.collapseInfo[0];
  }
  get collapseInfo() {
    const { panel } = this, headerDock2 = panel.hasHeader && panel.expandedHeaderDock;
    let dock = dockByDirection[this.collapseDir], transverse = false;
    if (headerDock2) {
      if (!(transverse = dockIsHorz[dock] !== dockIsHorz[headerDock2])) {
        dock = headerDock2;
      }
    }
    return [dock, transverse];
  }
  get toolsConfig() {
    const { direction, tool } = this;
    return tool && {
      collapse: ObjectHelper.assign({
        direction: direction.toLowerCase()
      }, tool)
    };
  }
  beforeCollapse(operation) {
    const { panel } = this, { element } = panel;
    if (element.matches(":focus-within")) {
      panel.revertFocus(true);
    }
  }
  changeTool(tool) {
    const me = this, { panel } = me;
    if (me.isConfiguring || me.isDestroying || !panel || panel.isDestroying) {
      return tool;
    }
    panel.tools = {
      collapse: tool
    };
  }
  collapse(collapsed) {
    var _a5, _b2;
    const me = this, { panel } = me, operation = {
      id: ++idSeed2,
      completed: false,
      panel
    };
    let { currentOperation } = me;
    collapsed = collapsed != null ? collapsed : true;
    if (ObjectHelper.isObject(collapsed)) {
      operation.collapsed = true;
      ObjectHelper.assign(operation, collapsed);
      collapsed = operation.collapsed;
      delete operation.collapsed;
    }
    operation.collapsing = collapsed;
    operation.previous = currentOperation != null ? currentOperation : null;
    if (collapsed !== me.collapsed) {
      if (currentOperation) {
        if (currentOperation.collapsing !== collapsed) {
          operation.animation = currentOperation.animation.revert({
            finalize() {
              var _a6;
              (_a6 = me.collapseFinalize) == null ? void 0 : _a6.call(me, operation, true);
            }
          });
          operation.collapsing = collapsed;
          currentOperation = operation;
        }
      } else {
        operation.animation = ObjectHelper.clone(("animation" in operation ? operation : me).animation);
        if (me.beforeCollapse(operation) !== false) {
          if (operation.animation) {
            operation.animation.finalize = (complete) => {
              var _a6;
              return (_a6 = me.collapseFinalize) == null ? void 0 : _a6.call(me, operation, complete);
            };
          }
          panel.changingCollapse = true;
          me[collapsed ? "collapseBegin" : "expandBegin"](operation);
          if (operation.animation) {
            currentOperation = operation;
          } else {
            operation.completed = true;
            me[collapsed ? "collapseEnd" : "expandEnd"](operation);
          }
          panel.changingCollapse = false;
          if (!operation.animation) {
            me.onComplete(collapsed ? "collapse" : "expand");
          }
        }
      }
    } else if (currentOperation && currentOperation.collapsing !== collapsed) {
      me[collapsed ? "expandRevert" : "collapseRevert"](operation);
      operation.animation = currentOperation.animation.revert({
        finalize() {
          var _a6;
          (_a6 = me.collapseFinalize) == null ? void 0 : _a6.call(me, operation, false);
        }
      });
      currentOperation = operation;
    }
    me.currentOperation = currentOperation;
    return (_b2 = (_a5 = currentOperation == null ? void 0 : currentOperation.animation) == null ? void 0 : _a5.done()) != null ? _b2 : Promise.resolve(collapsed === me.collapsed);
  }
  collapseFinalize(operation, complete) {
    const me = this, { currentOperation, panel } = me, action = panel.collapsed ? "expand" : "collapse";
    if (currentOperation === operation) {
      me.currentOperation = null;
      operation.completed = complete;
      panel.changingCollapse = true;
      me[action + "End"](operation);
      panel.changingCollapse = false;
      complete && me.onComplete(action);
    }
  }
  applyHeaderDock(collapsed, flush = true) {
    const me = this, { currentDock, panel } = me, { header } = panel, dock = collapsed ? me.collapseDock : panel.expandedHeaderDock;
    if (dock !== currentDock && panel.hasHeader) {
      panel.header = header ? typeof header === "string" ? { title: header } : ObjectHelper.clone(panel.header) : {};
      panel.header.dock = dock;
      flush && panel.recompose.flush();
    }
  }
  composeHeader(header) {
    const { panel } = this, { class: cls } = header, dock = panel.expandedHeaderDock || Object.keys(cls).filter((k) => cls[k] && headerDockRe.test(k)).map((k) => headerDockRe.exec(k)[1][0]);
    cls[revealerCls] = 1;
    cls[`b-collapsible-${dock[0]}${this.collapseDir[0]}`] = 1;
    return header;
  }
  composeTitle(title) {
    title.class[revealerCls] = 1;
    return title;
  }
  collapseBegin(operation) {
    var _a5;
    const me = this, { animation } = operation, { collapseDim, collapseTool, panel } = me, { element, placement } = panel, [collapseDock, transverse] = me.collapseInfo, collapseToolClasses = collapseTool == null ? void 0 : collapseTool.element.classList, unflex = !placement || placement[0] === "h" && collapseDim === "width" || placement[0] === "v" && collapseDim === "height";
    me.configuredWidth = panel._lastWidth;
    me.configuredHeight = panel._lastHeight;
    me.transverseCollapse = transverse;
    me.applyHeaderDock(true);
    const panelRect = panel.rectangle(), bodyWrapRect = me.lockInnerSize().moveTo(0, 0), bodyWrapClipRect = bodyWrapRect.clone(), clipDir = clipByDock[collapseDock], headerRect = (_a5 = panel.headerElement) == null ? void 0 : _a5.getBoundingClientRect(), collapsedSize = round((headerRect == null ? void 0 : headerRect[collapseDim]) || 0);
    element.classList.toggle(unflexCls, unflex);
    if (animation) {
      bodyWrapClipRect[clipDir[0]] = bodyWrapClipRect[clipDir[1]];
      element.classList.add("b-collapsing");
      collapseToolClasses == null ? void 0 : collapseToolClasses.add("b-collapsed", "b-collapsing");
      if (collapseTool) {
        collapseTool.collapsing = true;
      }
      if (collapseToolClasses) {
        collapseToolClasses.remove("b-collapsed");
        panel.rectangle();
        collapseToolClasses.remove("b-collapsing");
        collapseToolClasses.add("b-collapsed");
      }
      animation.element = element;
      animation.retain = true;
      animation[collapseDim] = {
        from: round(panelRect[collapseDim]),
        to: collapsedSize
      };
      animation.items = [{
        element: me.innerElement,
        retain: false,
        clip: {
          from: `rect(${bodyWrapRect})`,
          to: `rect(${bodyWrapClipRect})`
        }
      }];
      if (transverse) {
        animation.items.push({
          element: panel.headerElement,
          duration: animation.duration,
          retain: false,
          transform: {
            from: transverseTransform[collapseDock](headerRect),
            to: `translate(0, 0)`
          }
        });
      }
      operation.animation = Animator.run(animation);
    } else {
      element.style[collapseDim] = `${collapsedSize}px`;
      if (collapseTool) {
        collapseToolClasses.add("b-collapsing", "b-collapsed");
        collapseTool.rectangle();
        collapseToolClasses.remove("b-collapsing");
      }
    }
  }
  collapseEnd(operation) {
    const me = this, { collapseTool } = me;
    if (me.panel.isDestroyed) {
      return;
    }
    me.panel.element.classList.remove("b-collapsing");
    if (collapseTool) {
      collapseTool.collapsing = false;
    }
    if (operation.completed) {
      me.collapsed = true;
    } else {
      me.applyHeaderDock(false);
      me.restoreConfiguredSize();
      me.lockInnerSize(false);
    }
  }
  collapseRevert(operation) {
    var _a5;
    (_a5 = this.collapseTool) == null ? void 0 : _a5.element.classList.remove("b-collapsed");
  }
  expandBegin(operation) {
    const me = this, { animation } = operation, { collapseDim, collapseTool, panel } = me, [collapseDock, transverse] = me.collapseInfo, { element } = panel, elementClassList = element.classList, unflex = elementClassList.contains(unflexCls), fromRect = panel.rectangle();
    elementClassList.remove("b-collapsed", "b-collapsing");
    panel.element.style[collapseDim] = "";
    me.restoreConfiguredSize();
    me.lockInnerSize(false);
    const toRect = panel.rectangle(), bodyWrapRect = me.lockInnerSize().moveTo(0, 0), bodyWrapClipRect = bodyWrapRect.clone(), clipDir = clipByDock[me.collapseDock];
    collapseTool == null ? void 0 : collapseTool.element.classList.remove("b-collapsed");
    if (animation) {
      bodyWrapClipRect[clipDir[0]] = bodyWrapClipRect[clipDir[1]];
      elementClassList.add("b-collapsed", "b-expanding");
      unflex && elementClassList.add(unflexCls);
      animation.element = element;
      animation[collapseDim] = {
        from: round(fromRect[collapseDim]),
        to: round(toRect[collapseDim])
      };
      animation.items = [{
        element: me.innerElement,
        retain: false,
        clip: {
          from: `rect(${bodyWrapClipRect})`,
          to: `rect(${bodyWrapRect})`
        }
      }];
      if (transverse) {
        animation.items.push({
          element: panel.headerElement,
          duration: animation.duration,
          retain: false,
          transform: {
            from: `translate(0, 0)`,
            to: transverseTransform[collapseDock](fromRect)
          }
        });
      }
      operation.animation = Animator.run(animation);
    }
  }
  expandEnd(operation) {
    const me = this;
    me.panel.element.classList.remove("b-expanding");
    if (operation.completed) {
      me.collapsed = false;
      me.applyHeaderDock(false);
      me.restoreConfiguredSize();
      me.lockInnerSize(false);
    }
  }
  expandRevert(operation) {
    var _a5;
    (_a5 = this.collapseTool) == null ? void 0 : _a5.element.classList.add("b-collapsed");
  }
  get innerElement() {
    return this.panel.collapseWrapElement || this.panel.bodyWrapElement;
  }
  get innerSizeElement() {
    return this.transverseCollapse ? this.panel.element : this.innerElement;
  }
  get supportAxis() {
    let { _supportAxis } = this;
    const fullSupport = _supportAxis === true;
    if (fullSupport || _supportAxis == null) {
      _supportAxis = this.collapseDim[0];
      if (fullSupport || DomHelper.getStyleValue(this.panel.element, "position") === "absolute") {
        _supportAxis += crossAxis[_supportAxis];
      }
    }
    return _supportAxis || "";
  }
  lockInnerSize(lock = true) {
    const me = this, { innerElement, panel } = me, supportAxis = lock ? me.supportAxis : "", panelEl = panel.element, headerEl = panel.headerElement, headerRect = lock && headerEl && Rectangle.from(headerEl, panelEl), innerRect = lock && Rectangle.from(me.innerSizeElement, panelEl), innerStyle = innerElement.style;
    if (headerEl) {
      headerEl.style.minWidth = supportAxis.includes("w") ? `${headerRect.width}px` : "";
      headerEl.style.minHeight = supportAxis.includes("h") ? `${headerRect.height}px` : "";
    }
    innerStyle.width = lock ? `${innerRect.width}px` : "";
    innerStyle.height = lock ? `${innerRect.height}px` : "";
    innerElement.classList[lock ? "add" : "remove"]("b-panel-collapse-size-locker");
    return innerRect;
  }
  onCollapseClick(e) {
    let collapsed = this.collapsing ? false : this.expanding ? true : !this.collapsed;
    if (e.altKey) {
      collapsed = {
        animation: null,
        collapsed
      };
    }
    this.collapse(collapsed);
  }
  onComplete(action) {
    var _a5, _b2;
    (_b2 = (_a5 = this.panel) == null ? void 0 : _a5.trigger) == null ? void 0 : _b2.call(_a5, action);
  }
  onPanelHeaderClick({ event }) {
    if (event.button === 0 && this.panel.collapsed && event.target.classList.contains(revealerCls)) {
      this.onRevealerClick();
    }
  }
  onPanelConfigChange({ name, value: value2 }) {
    const me = this, { panel } = me;
    if (name === "collapsed") {
      if (panel.isPainted) {
        me.collapsed = value2;
      }
    } else if (name === "header" && !panel.changingCollapse) {
      me.syncDirection();
    }
  }
  onPanelPaint() {
    this.syncDirection();
    if (this.panel.collapsed && !this.collapsed) {
      this.collapse({
        animation: null,
        collapsed: true
      });
    }
  }
  onRevealerClick() {
    this.panel._collapse({ collapsed: false });
  }
  restoreConfiguredSize(which) {
    const { configuredHeight, configuredWidth, panel } = this;
    which = which != null ? which : "wh";
    panel.element.classList.remove(unflexCls);
    if (configuredWidth != null && which.includes("w")) {
      panel.width = configuredWidth;
    }
    if (configuredHeight != null && which.includes("h")) {
      panel.height = configuredHeight;
    }
  }
  splitHeaderItems({ as, dock } = emptyObject8) {
    var _a5;
    return (_a5 = this.panel) == null ? void 0 : _a5.splitHeaderItems({ as, dock, alt: true });
  }
  syncDirection() {
    const me = this, { direction } = me;
    if (!direction || defaultedDirectionRe.test(direction)) {
      me.direction = me.getCollapseDir();
    }
  }
  changeCollapsed(collapsed) {
    return Boolean(collapsed);
  }
  updateCollapsed(collapsed) {
    const { collapseTool, panel } = this;
    if (panel) {
      panel.collapsed = collapsed;
      panel.element.classList[collapsed ? "add" : "remove"]("b-collapsed");
    }
    if (collapseTool) {
      collapseTool.collapsed = collapsed;
    }
  }
  updateDirection(direction) {
    const { collapseTool, panel } = this;
    if (collapseTool) {
      collapseTool.direction = canonicalDirection[direction];
    }
    if (panel == null ? void 0 : panel.rendered) {
      panel.recompose();
    }
  }
  updatePanel(panel) {
    var _a5;
    const me = this;
    me.syncDirection();
    (_a5 = me.panelChangeDetacher) == null ? void 0 : _a5.call(me);
    me.panelChangeDetacher = panel && FunctionHelper.after(panel, "onConfigChange", "onPanelConfigChange", me, {
      return: false
    });
  }
  wrapCollapser(key, body) {
    var _a5;
    const me = this, [collapseDock, transverse] = me.collapseInfo;
    if (!transverse) {
      return [key, body];
    }
    const { collapseDir, panel } = me, { expandedHeaderDock, header: panelHeader, uiClassList } = panel, recollapse = (_a5 = panel.tools) == null ? void 0 : _a5.recollapse, [before, after] = me.splitHeaderItems({ as: "element", dock: collapseDock }), title = panel.hasHeader ? panel.title || (panelHeader == null ? void 0 : panelHeader.title) || "\xA0" : null, headerElement = title && {
      tag: "header",
      class: new DomClassList({
        ...uiClassList,
        [`b-dock-${expandedHeaderDock}`]: 1,
        "b-panel-header": 1,
        "b-panel-collapser-header": 1
      }, panelHeader == null ? void 0 : panelHeader.cls),
      style: panelHeader == null ? void 0 : panelHeader.style,
      children: [
        ...before,
        {
          reference: "collapserTitleElement",
          html: title,
          class: {
            ...uiClassList,
            [`b-align-${(panelHeader == null ? void 0 : panelHeader.titleAlign) || "start"}`]: 1,
            "b-header-title": 1
          }
        },
        ...after
      ]
    };
    if (recollapse) {
      recollapse.direction = collapseDir;
    }
    return [
      "collapseWrapElement",
      {
        class: {
          ...uiClassList,
          [`b-panel-collapser-header-${expandedHeaderDock}`]: 1,
          [`b-panel-collapser-${collapseDir}`]: 1,
          [`b-${dockIsHorz[expandedHeaderDock] ? "h" : "v"}box`]: 1,
          "b-panel-collapser": 1,
          "b-box-center": 1
        },
        children: dockBeforeRe.test(expandedHeaderDock) ? {
          collapserHeaderElement: headerElement,
          [key]: body
        } : {
          [key]: body,
          collapserHeaderElement: headerElement
        }
      }
    ];
  }
};
__publicField(PanelCollapser, "$name", "PanelCollapser");
__publicField(PanelCollapser, "type", "inline");
PanelCollapser.maps = {
  clipByDock,
  dockByDirection,
  dockIsHorz
};
PanelCollapser.initClass();
PanelCollapser._$name = "PanelCollapser";

// ../Core/lib/Core/util/DynamicObject.js
var PENDING = Symbol("pendingCreate");
var DynamicObject = class extends Base {
  static get prototypeProperties() {
    return {
      /**
       * Optional function that will be passed an instance prior to destroying it.
      *
       * @config {Function}
       * @param {String} name The property name in the Dynamic object by which the new instance may be referenced
       * @param {Object} instance The value of the property
       * @returns {void}
       */
      cleanup: null,
      /**
       * Optional name of the config property managed by this instance. If changes are made directly, this
       * property is used to run the `onConfigChange` method of the `owner`.
       * @config {String}
       */
      configName: null,
      /**
       * Optional function to call as instances are created. Each new instance is passed to this function.
       *
       * @config {Function}
       * @param {Object} instance The newly created instance.
       * @param {String} key The property name in the dynamic object by which the new instance may be referenced.
       * @returns {void}
       */
      created: null,
      /**
       * The {@link Core.mixin.Factoryable factory} to use to create instances.
       * @config {Object}
       */
      factory: null,
      /**
       * By default, the name of the member is used for the type. Set this config to `true` to also allow the
       * config object for a property to contain a `type` property. Set this to `false` to ignore the name of the
       * member and rely on the {@link #config-factory} to process the config object.
       * @config {Boolean|String}
       * @default
       */
      inferType: "name",
      /**
       * The owning object to pass along to the instances as the `ownerName` property.
       * @config {Object}
       */
      owner: null,
      /**
       * The property name by which to store the `owner` on each instance.
       * @config {String}
       */
      ownerName: null,
      /**
       * Set to `false` to prevent using a `Proxy` even if that JavaScript platform feature is available. Using
       * a `Proxy` is ideal because it allows for all forms of access to the dynamic properties to be handled
       * instead of only those that have predefined configuration values.
       * @config {Boolean}
       * @private
       */
      proxyable: typeof Proxy !== "undefined",
      /**
       * Optional function that will be passed a config object prior to instantiating an object. This function
       * can either modify the passed object or return a new object.
       *
       * @config {Function}
       * @param {Object} config The config object used to create the object
       * @param {String} name The property name in the Dynamic object by which the new instance may be referenced
       * @returns {Object|null}
       */
      setup: null,
      /**
       * Optional function that will be passed a raw config object prior to processing and the value it returns
       * replaces the raw value. This function is used to transform strings or arrays (for example) into proper
       * config objects.
       *
       * @config {Function}
       * @param {*} config The original value of the config object parameter
       * @returns {*}
       */
      transform: null
    };
  }
  static get properties() {
    return {
      /**
       * Holds config objects for each defined object. These are used to hold class and instance config values
       * and use them to create instances on first request, or when `flush()` is called. Further, if the instance
       * is initially assigned instead of retrieved, these values act as the defaults for the instance and are
       * combined with those provided in the assignment.
       * @member {Object} defaults
       * @private
       */
      defaults: {},
      /**
       * This object holds the actual instances that are retrieved by the dynamic accessor or `Proxy`.
       * @member {Object} instances
       * @private
       */
      instances: {},
      /**
       * The object that contains the dynamic accessors for each instance. This object is not used when using a
       * `Proxy`.
       * @member {Object} object
       * @private
       */
      object: /* @__PURE__ */ Object.create({})
    };
  }
  /**
   * Returns the `Proxy` instance used to manage dynamic assignments. If the JavaScript platform does not support the
   * `Proxy` class, this will be `null`.
   * @property {Proxy}
   * @private
   */
  get proxy() {
    const me = this;
    let proxy = null;
    if (me.proxyable) {
      proxy = new Proxy(me.instances, {
        get(o, name) {
          return me.get(name);
        },
        set(o, name, value2) {
          me.set(name, value2);
          return true;
        },
        deleteProperty(o, name) {
          me.set(name, null);
          return true;
        }
      });
    }
    Reflect.defineProperty(me, "proxy", {
      configurable: true,
      // allow destroy() to delete it
      value: proxy
    });
    return proxy;
  }
  /**
   * Returns the object that contains the dynamic properties. This may be a `Proxy` instance or an object with getter
   * and setter accessors.
   * @property {Object}
   * @internal
   */
  get target() {
    return this.proxy || this.object;
  }
  /**
   * This method establishes the initial definition of a dynamic property. When using a `Proxy`, this method simply
   * needs to cache away the initial config for use by the getter. When `Proxy` is unavailable, this method will
   * also defined a getter/setter to intercept access to the dynamic property.
   * @param {String} name The name of the dynamic property.
   * @param {Object} config The initial config object for the dynamic property.
   * @private
   */
  define(name, config) {
    const me = this, { transform } = me, transformed = transform ? transform(config, name) : config, instantiated = ObjectHelper.isInstantiated(transformed);
    me.instances[name] = PENDING;
    if (!instantiated) {
      me.setDefaults(name, transformed);
    }
    me.defineProp(name, true);
    if (instantiated) {
      me.set(name, transformed);
    }
  }
  /**
   * Define the get/set accessors for `name` on our `object` or its prototype.
   * @param {String} name
   * @param {Boolean} [base] Pass `true` to indicate the property should be defined on the prototype.
   * @private
   */
  defineProp(name, base) {
    const me = this, { object } = me;
    if (!me.proxy) {
      Reflect.defineProperty(base ? Object.getPrototypeOf(object) : object, name, {
        configurable: !base,
        enumerable: true,
        get() {
          return me.get(name);
        },
        set(value2) {
          return me.set(name, value2);
        }
      });
    }
  }
  /**
   * Ensures that all defined members are touched to trigger their creation.
   * @internal
   */
  flush() {
    var _a5;
    const me = this;
    try {
      me.updating = true;
      for (const name in me.defaults) {
        me.get(name);
      }
      me.afterConfigureOwner = (_a5 = me.afterConfigureOwner) == null ? void 0 : _a5.call(me);
    } finally {
      me.updating = false;
    }
  }
  /**
   * Returns (lazily creating as necessary) the value of a dynamic property given its name.
   * @param {String} name
   * @returns {Object}
   * @private
   */
  get(name) {
    const { defaults, instances } = this;
    if (instances[name] === PENDING) {
      this.set(name, PENDING);
    }
    return instances[name] || defaults[name] && null;
  }
  /**
   * Sets the value of a dynamic property given its name and value.
   * @param {String} name
   * @param {Object} value
   * @private
   */
  set(name, value2) {
    var _a5;
    const me = this, { cleanup, configName, defaults, factory, instances, owner, setup, transform, updating } = me, inform = owner && configName && !updating, was = instances[name] === PENDING ? null : instances[name], instance = factory.reconfigure(was, value2 === PENDING ? {} : value2 || null, {
      cleanup: cleanup && ((instance2) => cleanup(instance2, name)),
      defaults: defaults[name] || me.setDefaults(name, {}),
      owner: me.owner,
      setup: setup && ((config, type, defaults2) => setup(config, name, type, defaults2)),
      transform: transform && ((config) => transform(config, name))
    });
    if (instance !== was) {
      const before = inform && { ...instances };
      instances[name] = instance;
      if (instance) {
        me.defineProp(name);
        (_a5 = me.created) == null ? void 0 : _a5.call(me, instance, name);
      } else {
        delete me.object[name];
        delete instances[name];
      }
      if (inform) {
        owner.onConfigChange({
          name: configName,
          config: owner.$meta.configs[configName],
          value: me.target,
          was: before
        });
      }
    }
  }
  /**
   * Stores the default config values for use in the factory reconfiguration process.
   * @param {String} name The name and default type of the dynamic property. Depending on {@link #config-inferType},
   * this may not be overridden by a type property in the config object.
   * @param {Object} config The config object.
   * @returns {Object}
   * @private
   */
  setDefaults(name, config) {
    const { defaults, factory, inferType, instances, owner, ownerName } = this, { typeKey } = factory.factoryable;
    config = config === true ? {} : ObjectHelper.assign({}, config);
    if (inferType === "name" || inferType === true && !config[typeKey]) {
      config[typeKey] = name;
    }
    if (ownerName) {
      config[ownerName] = owner;
    }
    config.beforeConfigure = (instance) => {
      instances[name] = instance;
    };
    return defaults[name] = config;
  }
  /**
   * Updates the members of `object` based on the provided configuration.
   * @param {Object} members The configuration for the instances of `object`.
   * @internal
   */
  update(members) {
    const me = this, { owner } = me;
    let name, config;
    try {
      me.updating = true;
      if (members) {
        for (name in members) {
          config = members[name];
          if (me.defaults[name]) {
            me.set(name, config);
          } else if (config) {
            me.define(name, config);
          }
        }
      } else {
        for (name in me.instances) {
          me.set(name, null);
        }
      }
    } finally {
      me.updating = false;
    }
    if (owner) {
      if (!owner.isConfiguring) {
        me.flush();
      } else if (!me.afterConfigureOwner) {
        me.afterConfigureOwner = FunctionHelper.before(owner, "afterConfigure", "flush", me);
      }
    }
  }
};
DynamicObject.initClass();
DynamicObject._$name = "DynamicObject";

// ../Core/lib/Core/widget/mixin/Toolable.js
var emptyArray6 = [];
var emptyObject9 = {};
var toolConfigs = {
  align: 1,
  // hidden : 1,
  weight: 1
};
var Toolable_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Widget) {
    static get configurable() {
      return {
        /**
         * The {@link Core.widget.Tool tools} as specified by the {@link #config-tools} configuration. Each is a
         * {@link Core.widget.Tool} instance which may be hidden, shown and observed and styled just like any other
         * widget.
         * @member {Object<String,Core.widget.Tool>} tools
         * @accepts {Object<String,Core.widget.Tool|ToolConfig>}
         * @category Content
         */
        /**
         * The {@link Core.widget.Tool tools} to add either before or after the `title` in the Panel header. Each
         * property name is the reference by which an instantiated tool may be retrieved from the live
         * `{@link #property-tools}` property.
         * @config {Object<String,ToolConfig>} tools
         * @category Content
         */
        tools: {
          value: null,
          $config: {
            nullify: true
          }
        },
        /**
         * An object containing config defaults for corresponding {@link #config-tools} objects with a matching name.
         *
         * This object contains a key named `'*'` with default config properties to apply to all tools. This
         * object provides the default `type` (`'tool').
         * @config {Object} toolDefaults
         * @private
         */
        toolDefaults: {
          "*": {
            type: "tool",
            align: "end"
          }
        }
      };
    }
    byWeightSortFn(a, b) {
      return (a.weight || 0) - (b.weight || 0);
    }
    byWeightReverseSortFn(a, b) {
      return (b.weight || 0) - (a.weight || 0);
    }
    gatherTools({ align, alt: alt2, refs } = emptyObject9) {
      var _a6, _b2, _c2;
      const { collapsed, tools } = this, options = { collapsed, alt: alt2 };
      let ret = [], alignment, key, i, item, tool;
      for (key in tools) {
        tool = tools[key];
        alignment = (_c2 = (_b2 = (_a6 = tool == null ? void 0 : tool.align) == null ? void 0 : _a6.align) != null ? _b2 : tool == null ? void 0 : tool.align) != null ? _c2 : "end";
        if (alignment === align && tool.isCollapsified(options)) {
          ret.push(tool);
        }
      }
      ret.sort(this[align === "end" ? "byWeightReverseSortFn" : "byWeightSortFn"]);
      if (refs) {
        const asWidget = refs === "widget", asRefs = {};
        for (i = 0; i < ret.length; ++i) {
          item = ret[i];
          asRefs[item.ref] = asWidget ? item : item.element;
        }
        ret = asRefs;
      }
      return ret;
    }
    getEndTools({ alt: alt2, refs } = emptyObject9) {
      return this.gatherTools({ align: "end", alt: alt2, refs });
    }
    getStartTools({ alt: alt2, refs } = emptyObject9) {
      return this.gatherTools({ align: "start", alt: alt2, refs });
    }
    get childItems() {
      return [
        ...this.getStartTools(),
        ...this._items || emptyArray6,
        ...this.getEndTools()
      ];
    }
    changeTools(tools, oldTools) {
      const me = this, manager = me.$tools || (me.$tools = new DynamicObject({
        configName: "tools",
        factory: Widget,
        inferType: false,
        // the name of a tool in the tools object is not its type
        owner: me,
        created(instance) {
          var _a6, _b2;
          instance.innerItem = false;
          (_b2 = instance.syncRotationToDock) == null ? void 0 : _b2.call(instance, (_a6 = me.header) == null ? void 0 : _a6.dock);
          FunctionHelper.after(instance, "onConfigChange", (ret, { name }) => {
            if (toolConfigs[name]) {
              me.onConfigChange({
                name: "tools",
                value: manager.target
              });
            }
          });
          me.onChildAdd(instance);
        },
        setup(config, name) {
          config = ObjectHelper.merge({}, me.toolDefaults["*"], me.toolDefaults[name], config);
          config.parent = me;
          config.ref = name;
          return config;
        }
      }));
      manager.update(tools);
      if (!oldTools) {
        return manager.target;
      }
    }
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "Toolable"), _a5;
};

// ../Core/lib/Core/widget/layout/Box.js
var directionCls = [
  "b-hbox",
  "b-vbox"
];
var syncAlign = {
  name: "align",
  style: "alignItems"
};
var syncContentAlign = {
  name: "contentAlign",
  style: "alignContent"
};
var syncDirection = {
  name: "direction",
  style: "flexDirection"
};
var syncJustify = {
  name: "justify",
  style: "justifyContent",
  classes: ["stretch"]
};
var syncWrap = {
  name: "wrap",
  style: "flexWrap",
  map: {
    false: "nowrap",
    true: "wrap",
    reverse: "wrap-reverse"
  }
};
var Box = class extends Layout {
  /**
   * Set this value to `true` to set the [flex-direction](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-direction)
   * style of the {@link #property-owner owner's} {@link Core.widget.Widget#property-contentElement}
   * to `column`. Or alternatively, set {@link #config-horizontal} to `false`.
   * @config {Boolean} vertical
   * @default false
   */
  get vertical() {
    return this.horizontal === false;
  }
  set vertical(v) {
    return this.horizontal = !v;
  }
  updateAlign() {
    this.syncConfigStyle(syncAlign);
  }
  updateContentAlign() {
    this.syncConfigStyle(syncContentAlign);
  }
  updateDirection() {
    this.syncConfigStyle(syncDirection);
  }
  updateHorizontal() {
    var _a5;
    const me = this, classList = (_a5 = me.contentElement) == null ? void 0 : _a5.classList, vertical = Number(me.vertical);
    if (classList) {
      classList.remove(directionCls[1 - vertical]);
      classList.add(directionCls[vertical]);
    } else {
      me.syncConfigLater("horizontal");
    }
    me.syncDirection();
  }
  updateJustify() {
    this.syncConfigStyle(syncJustify);
  }
  updateReverse() {
    this.syncDirection();
  }
  updateWrap() {
    this.syncConfigStyle(syncWrap);
  }
  syncDirection() {
    const me = this, { reverse } = me;
    me.direction = reverse ? `${me.vertical ? "column" : "row"}${reverse ? "-reverse" : ""}` : null;
  }
  changeGap(value2) {
    if (typeof value2 === "number") {
      value2 = `${value2}px`;
    }
    return value2;
  }
  updateGap(value2) {
    this.syncConfigStyle({ name: "gap", style: "gap" });
  }
};
__publicField(Box, "$name", "Box");
__publicField(Box, "type", "box");
__publicField(Box, "alias", "hbox");
__publicField(Box, "configurable", {
  containerCls: "b-box-container",
  itemCls: "b-box-item",
  /**
   * Sets the [align-items](https://developer.mozilla.org/en-US/docs/Web/CSS/align-items) style of the
   * {@link #property-owner owner's} {@link Core.widget.Widget#property-contentElement}.
   * @config {String} align
   * @default 'stretch'
   */
  align: null,
  /**
   * Sets the [align-content](https://developer.mozilla.org/en-US/docs/Web/CSS/align-content) style of the
   * {@link #property-owner owner's} {@link Core.widget.Widget#property-contentElement}.
   * @config {String} contentAlign
   * @default 'normal'
   */
  contentAlign: null,
  /**
   * Sets the [direction](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-direction) style of the
   * {@link #property-owner owner's} {@link Core.widget.Widget#property-contentElement}.
   * This config is not set directly. Set {@link #config-horizontal}, {@link #config-vertical}, and/or
   * {@link #config-reverse} instead.
   * @config {String} direction
   * @private
   */
  direction: null,
  /**
   * Set this value to `false` to set the [flex-direction](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-direction)
   * style of the {@link #property-owner owner's} {@link Core.widget.Widget#property-contentElement}
   * to `column`. Or alternatively, set {@link #config-vertical} to `true`.
   * @config {Boolean} horizontal
   * @default
   */
  horizontal: true,
  /**
   * Sets the [justify-content](https://developer.mozilla.org/en-US/docs/Web/CSS/justify-content) style of the
   * {@link #property-owner owner's} {@link Core.widget.Widget#property-contentElement}.
   * @config {String} justify
   * @default 'flex-start'
   */
  justify: null,
  /**
   * Set this value to `true` to add `'-reverse'` to the [flex-direction](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-direction)
   * style of the {@link #property-owner owner's} {@link Core.widget.Widget#property-contentElement}.
   * This config combines with {@link #config-horizontal} or {@link #config-vertical} to set the `flex-direction`
   * style.
   * @config {Boolean} reverse
   * @default false
   */
  reverse: null,
  /**
   * Sets the [flex-wrap](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-wrap) style of the
   * {@link #property-owner owner's} {@link Core.widget.Widget#property-contentElement}.
   *
   * The value of `true` is equivalent to `'wrap'`, `false` is equivalent to `'nowrap'`, and `'reverse'` is
   * equivalent to `'wrap-reverse'`.
   *
   * ```javascript
   *  layout : {
   *      type : 'box',
   *      wrap : false        // equivalent to 'nowrap'
   *      wrap : true         // equivalent to 'wrap'
   *      wrap : 'reverse'    // equivalent to 'wrap-reverse'
   *  }
   * ```
   * @config {String|Boolean} wrap
   * @default false
   */
  wrap: null,
  /**
   * The amount of gap between the element of child items.
   * @prp {Number|String}
   */
  gap: null
});
Box.initClass();
Box._$name = "Box";

// ../Core/lib/Core/widget/layout/VBox.js
var VBox = class extends Box {
};
__publicField(VBox, "$name", "VBox");
__publicField(VBox, "type", "vbox");
__publicField(VBox, "configurable", {
  horizontal: false
});
VBox.initClass();
VBox._$name = "VBox";

// ../Core/lib/Core/widget/mixin/Badge.js
var Badge_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    static get configurable() {
      return {
        /**
         * Get/sets and display badge, set to null or empty string to hide.
         * @member {String} badge
         * @category Misc
         */
        /**
         * Initial text to show in badge.
         * @config {String} badge
         * @category Misc
         */
        badge: null
      };
    }
    compose() {
      const { badge } = this;
      return {
        dataset: {
          badge
        },
        class: {
          "b-badge": badge != null && badge !== ""
        }
      };
    }
  }, __publicField(_a5, "$name", "Badge"), _a5;
};

// ../Core/lib/Core/widget/Button.js
var bIcon = /(?:^|\s)b-icon-/;
var bFa = /(?:^|\s)b-fa-/;
var fullConfigKeys = ["items", "type", "widgets", "html", "listeners"];
var menuListenersName = Symbol("defaultListener");
var Button = class extends Widget.mixin(Badge_default, Rotatable_default) {
  static get configurable() {
    return {
      /**
       * Button icon class.
       *
       * All [Font Awesome](https://fontawesome.com/cheatsheet) icons may also be specified as `'b-fa-' + iconName`.
       *
       * Otherwise this is a developer-defined CSS class string which results in the desired icon.
       *
       * This may also be configured as the URL of an image (Detected by searching for a `/` in the string).
       * @prp {String}
       */
      icon: null,
      /**
       * The menu icon class to show when the button has a menu. Set to `null` to not show a menu icon.
       *
       * All [Font Awesome](https://fontawesome.com/cheatsheet) icons may also be specified as `'b-fa-' + iconName`.
       *
       * @prp {String}
       * @default
       */
      menuIcon: "b-icon-picker",
      /**
       * Icon class for the buttons pressed state. Only applies to toggle buttons
       *
       * All [Font Awesome](https://fontawesome.com/cheatsheet) icons may also be specified as `'b-fa-' + iconName`.
       *
       * Otherwise this is a developer-defined CSS class string which results in the desired icon.
       *
       * ```
       * new Button({
       *    // Icon for unpressed button
       *    icon        : 'b-fa-wine-glass',
       *
       *    // Icon for pressed button
       *    pressedIcon : 'b-fa-wine-glass-alt',
       *
       *    // Only applies to toggle buttons
       *    toggleable  : true
       * });
       * ```
       *
       * @prp {String}
       */
      pressedIcon: null,
      /**
       * A CSS class to add to the pressed state of the button. A `b-pressed` CSS class is always added,
       * when a button is pressed.
       * @prp {String}
       * @default
       */
      pressedCls: null,
      /**
       * Button icon alignment. May be `'start'` or `'end'`. Defaults to `'start'`
       * @prp {'start'|'end'}
       * @default
       */
      iconAlign: "start",
      /**
       * The button behavioral type, will be applied as a `type` attribute to this button's element.
       * @prp {'button'|'submit'|'reset'}
       * @default
       */
      behaviorType: "button",
      /**
       * The button's text.
       * @prp {String|DomConfig|DomConfig[]}
       */
      text: {
        value: null,
        $config: null,
        default: ""
      },
      /**
       * Button color (should have match in button.scss or your custom styling). Valid values in Bryntum themes
       * are:
       * * b-amber
       * * b-blue
       * * b-dark-gray
       * * b-deep-orange
       * * b-gray
       * * b-green
       * * b-indigo
       * * b-lime
       * * b-light-gray
       * * b-light-green
       * * b-orange
       * * b-purple
       * * b-red
       * * b-teal
       * * b-white
       * * b-yellow
       * Combine with specifying `b-raised` for raised/filled style (theme dependent).
       *
       * ```
       * new Button({
       *    color : 'b-teal b-raised'
       * });
       * ```
       *
       * @prp {String}
       */
      color: null,
      /**
       * Enabled toggling of the button (stays pressed when pressed).
       * @prp {Boolean}
       * @default false
       */
      toggleable: void 0,
      /**
       * Initially pressed or not. Only applies with `toggleable = true`.
       *
       * ```javascript
       * const toggleButton = new Button({
       *    toggleable : true,
       *    text : 'Enable cool action'
       * });
       * ```
       * @prp {Boolean}
       * @default
       */
      pressed: false,
      /**
       * Indicates that this button is part of a group where only one button can be pressed. Assigning a value
       * also sets `toggleable` to `true`.
       *
       * When part of a {@link Core.widget.ButtonGroup}, you can set {@link Core.widget.ButtonGroup#config-toggleGroup}
       * on it as an alternative to on each button. This config can then be used to override that value if needed.
       *
       * ```javascript
       * const yesButton = new Button({
       *    toggleGroup : 'yesno',
       *    text        : 'Yes'
       * });
       *
       * const noButton = new Button({
       *    toggleGroup : 'yesno',
       *    text        : 'No'
       * });
       * ```
       * @prp {String}
       */
      toggleGroup: null,
      /**
       * Set to `true` to perform action on clicking the button if it's already pressed
       * and belongs to a {@link #config-toggleGroup}.
       * @config {Boolean}
       * @default
       */
      supportsPressedClick: false,
      supportsUnpressing: false,
      ripple: {
        radius: 75
      },
      twinForwardEvents: {
        item: "action"
      },
      // Button has to calculate its twinSyncCOnfigs at runtime, so must not be set
      twinSyncConfigs: null,
      localizableProperties: ["text"],
      /**
       * Returns the instantiated menu widget as configured by {@link #config-menu}.
       * @member {Core.widget.Widget} menu
       */
      /**
       * A submenu configuration object, or an array of MenuItem configuration objects from which to create a
       * submenu which is shown when this button is pressed.
       *
       * Note that this does not have to be a Menu. The `type` config can be used to specify any widget as the
       * submenu.
       *
       * May also be specified as a fully instantiated {@link Core.widget.Widget#config-floating floating Widget}
       * such as a {@link Core/widget/Popup}.
       * @config {Object<String,MenuItemConfig|ContainerItemConfig>|Array<MenuItemConfig|ContainerItemConfig>|MenuConfig|PopupConfig}
       */
      menu: {
        $config: ["lazy", "nullify"],
        value: null
      },
      menuDefaults: {
        type: "menu",
        autoShow: false,
        autoClose: true,
        floating: true,
        scrollAction: "realign",
        align: "t0-b0"
      },
      /**
       * If provided, turns the button into a link.
       * <div class="note">Not compatible with the {@link Core.widget.Widget#config-adopt} config.</div>
       * @prp {String}
       */
      href: null,
      /**
       * The `target` attribute for the {@link #config-href} config
       * @prp {String}
       */
      target: null,
      /**
       * The tab index of the button, or `null` for natural tab order (recommended). Setting to `0` is equivalent
       * to natural tab order, but is unnecessary for buttons since they are naturally tabbable (i.e., accessible
       * via the TAB key). Setting to `-1` disables tabbability but allows for focus to be set to the element
       * programmatically.
       *
       * From [MDN documentation](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/tabindex):
       *
       * **Warning**: You are recommended to only use `0` and `-1` as tabindex values. Avoid using tabindex values
       * greater than 0 and CSS properties that can change the order of focusable HTML elements ([Ordering flex
       * items](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_flexible_box_layout/Ordering_flex_items)).
       * Doing so makes it difficult for people who rely on using keyboard for navigation or assistive technology
       * to navigate and operate page content. Instead, write the document with the elements in a logical sequence.
       *
       * @prp {Number} tabIndex
       */
      tabIndex: null,
      // Our own setValues/getValues system should not set/get HTML content
      defaultBindProperty: null,
      /**
       * Set to `true` to create a split button. A split button has a main action and a dropdown arrow to show a menu.
       *
       * The menu only shows when the arrow is clicked, not when the main button is clicked.
       *
       * The button will only toggle its {@link #property-pressed} state and action its `click` handler when the main
       * part of the button is clicked.
       * @prp {Boolean} split
       * @default false
       */
      split: null,
      keyMap: {
        ArrowDown: "onArrowDown"
      },
      role: ""
    };
  }
  construct(...args) {
    super.construct(...args);
    this.ion({ hide: "onInternalHide" });
  }
  // Twin MenuItem must not have a checked property unless we are toggleable
  // Twin MenuItem must have onItem which must be whichever exists of our onAction or onClick
  get twinSyncConfigs() {
    const result = {
      onAction: "onItem",
      onClick: "onItem"
    };
    if (this.toggleable && !this.hasConfig("menu")) {
      result.pressed = "checked";
    }
    return result;
  }
  updateElement(element, oldElement) {
    var _a5, _b2, _c2;
    const me = this, { constructor } = me, result = super.updateElement(element, oldElement), menu = me.peekConfig("menu"), role = menu ? menu.isWidget ? menu.role : ((_b2 = (_a5 = constructor.resolveType(menu.type)) == null ? void 0 : _a5.configurable) == null ? void 0 : _b2.role) || ((_c2 = constructor.configurable.menuDefaults) == null ? void 0 : _c2.type) || "menu" : false;
    me.ariaHasPopup = role;
    return result;
  }
  compose() {
    const {
      color,
      href,
      icon,
      iconAlign,
      pressed,
      pressedIcon,
      target,
      text,
      toggleable,
      toggleGroup,
      menuIcon,
      behaviorType,
      tabIndex,
      pressedCls,
      split: split2
    } = this, hasMenu = this.hasConfig("menu"), iconCls = pressed && pressedIcon ? pressedIcon : icon, hasCustomContent = typeof text === "object";
    return {
      tag: href ? "a" : "button",
      href,
      target,
      tabIndex,
      type: behaviorType,
      class: {
        [`b-icon-align-${iconAlign}`]: icon,
        [color]: Boolean(color),
        "b-pressed": pressed && toggleable,
        "b-text": Boolean(text),
        "b-has-menu": hasMenu,
        "b-splitbutton": split2,
        ...pressed && pressedCls ? Object.fromEntries(pressedCls.split(" ").map((cls) => [cls, 1])) : {}
      },
      [toggleable && !hasMenu ? "aria-pressed" : ""]: pressed,
      dataset: {
        group: toggleGroup
      },
      // eslint-disable-next-line bryntum/no-listeners-in-lib
      listeners: {
        click: "onInternalClick",
        mousedown: "onInternalMousedown"
      },
      children: {
        // Presence of a slash indicates an image URL
        iconElement: (icon == null ? void 0 : icon.includes("/")) ? {
          // This element is a purely visual cue with no meaning to the A11Y tree
          "aria-hidden": true,
          src: icon,
          tag: "img",
          class: {
            "b-button-icon": 1,
            "b-button-image": 1
          }
        } : (icon || pressedIcon) && {
          // This element is a purely visual cue with no meaning to the A11Y tree
          "aria-hidden": true,
          tag: "i",
          class: {
            ...DomClassList.normalize(iconCls, "object"),
            "b-button-icon": 1,
            "b-icon": bIcon.test(iconCls),
            "b-fa": bFa.test(iconCls)
          }
        },
        label: text && this.ariaLabel !== text && {
          tag: "label",
          for: this.id,
          [hasCustomContent ? "children" : "text"]: hasCustomContent ? ArrayHelper.asArray(text) : text,
          class: {
            "b-button-custom-content": hasCustomContent
          }
        },
        menuIconElement: hasMenu && menuIcon && {
          tag: "i",
          class: {
            "b-icon": bIcon.test(menuIcon),
            "b-fa": bFa.test(menuIcon),
            "b-button-menu-icon": 1,
            [menuIcon]: 1
          }
        }
      }
    };
  }
  //endregion
  onInternalHide() {
    var _a5;
    (_a5 = this._menu) == null ? void 0 : _a5.hide();
  }
  /**
   * Iterate over all widgets owned by this widget and any descendants.
   *
   * *Note*: Due to this method aborting when the function returns `false`, beware of using short form arrow
   * functions. If the expression executed evaluates to `false`, iteration will terminate.
   *
   * _Due to the {@link #config-menu} config being a lazy config and only being converted to be a
   * `Menu` instance just before it's shown, the menu will not be part of the iteration before
   * it has been shown once_.
   * @function eachWidget
   * @param {Function} fn A function to execute upon all descendant widgets.
   * Iteration terminates if this function returns `false`.
   * @param {Boolean} [deep=true] Pass as `false` to only consider immediate child widgets.
   * @returns {Boolean} Returns `true` if iteration was not aborted by a step returning `false`
   */
  get childItems() {
    return this._menu && [this.menu];
  }
  onFocusOut(e) {
    var _a5;
    super.onFocusOut(e);
    (_a5 = this.menu) == null ? void 0 : _a5.hide();
  }
  //region Getters/Setters
  get focusElement() {
    return this.element;
  }
  changeText(text) {
    return text == null ? "" : text;
  }
  changeToggleable(toggleable) {
    if (toggleable === void 0) {
      toggleable = Boolean(this.toggleGroup || this.config.menu);
    }
    return toggleable;
  }
  changeMenu(menu, oldMenu) {
    const me = this, { element: forElement } = me;
    if (menu) {
      if (menu.isWidget) {
        menu.forElement = forElement;
        menu.owner = me;
        menu.constrainTo = me.rootElement;
      } else {
        if (typeof menu === "object" && !fullConfigKeys.some((key) => key in menu)) {
          menu = {
            lazyItems: menu
          };
        }
        menu = Widget.reconfigure(oldMenu, menu ? ObjectHelper.merge({
          owner: me,
          constrainTo: me.rootElement,
          forElement
        }, me.menuDefaults, menu) : null, me);
      }
      menu.align.constrainPadding = 10;
      me.detachListeners(menuListenersName);
      menu.ion({
        name: menuListenersName,
        beforeShow: "onMenuBeforeShow",
        hide: "onMenuHide",
        show: "onMenuShow",
        thisObj: this
      });
    } else {
      oldMenu == null ? void 0 : oldMenu.destroy();
    }
    return menu;
  }
  onMenuBeforeShow({ source }) {
    return this.trigger("beforeShowMenu", {
      menu: source
    });
  }
  onMenuShow() {
    this.ariaElement.setAttribute("aria-expanded", true);
  }
  onMenuHide() {
    this.ariaElement.setAttribute("aria-expanded", false);
    this.toggle(false);
  }
  updateMenu(menu) {
    this.toggleable = Boolean(menu);
  }
  updatePressed(pressed) {
    const me = this;
    if (!me.toggleable || me.isConfiguring) {
      return;
    }
    if (pressed) {
      DomHelper.forEachSelector(me.rootElement, `button[data-group=${me.toggleGroup}]`, (btnEl) => {
        if (btnEl !== me.element) {
          Widget.getById(btnEl.id).pressed = false;
        }
      });
    }
    me.toggleMenu(me.split ? false : pressed);
    me.trigger("toggle", { pressed, userAction: Boolean(me.triggeredByEvent) });
  }
  //endregion
  configureOverflowTwin(overrides) {
    const me = this, config = super.configureOverflowTwin(overrides), { menu } = me;
    config.type = Widget.resolveType("menuitem");
    if (menu) {
      config.menu = me.constructor.mergeConfigs(config.menu, {
        type: "menu",
        items: menu.items.map((it) => it.ensureOverflowTwin())
      });
    } else if (me.toggleable) {
      config.checked = me.pressed;
    }
    if (!config.text) {
      config.text = me.text || me.tooltipText;
    }
    return config;
  }
  //region Events
  onInternalMousedown(event) {
    var _a5;
    if (BrowserHelper.isSafari || ((_a5 = this._menu) == null ? void 0 : _a5.containsFocus) && this.pressed) {
      event.preventDefault();
    }
  }
  changeTooltip(tooltip, oldTooltip) {
    const me = this, stringTooltip = (tooltip == null ? void 0 : tooltip.html) || tooltip;
    if (!me.ariaLabel && !me.text && typeof stringTooltip === "string") {
      me.ariaLabel = me.optionalL(stringTooltip, me);
    }
    return super.changeTooltip(tooltip, oldTooltip);
  }
  onArrowDown() {
    if (this.menu) {
      this.toggleMenu(true);
    } else {
      return false;
    }
  }
  /**
   * Triggers events when user clicks button
   * @fires click
   * @fires action
   * @internal
   */
  onInternalClick(event) {
    var _a5;
    const me = this, bryntumEvent = { event };
    if (me.disabled) {
      return;
    }
    if (me.split && event.target.closest(".b-button-menu-icon")) {
      me.toggleMenu(!((_a5 = me._menu) == null ? void 0 : _a5.isVisible));
      return;
    }
    me.triggeredByEvent = event;
    if (me.toggleable) {
      const skipToggle = me.toggleGroup && me.pressed && !me.supportsUnpressing;
      if (skipToggle && !me.supportsPressedClick) {
        return;
      }
      if (!skipToggle) {
        me.toggle(!me.pressed);
      }
      if (me.isDestroyed) {
        return;
      }
    }
    me.trigger("click", bryntumEvent);
    if (!me.isDestroyed) {
      me.trigger("action", bryntumEvent);
    }
    if (!me.href) {
      event.preventDefault();
      event.stopPropagation();
    }
    me.triggeredByEvent = null;
  }
  //endregion
  //region Misc
  /**
   * Toggle button state (only use with toggleable = true)
   * @param {Boolean} pressed Specify to force a certain toggle state
   * @fires toggle
   */
  toggle(pressed = !this.pressed) {
    if (this.trigger("beforeToggle", { pressed, userAction: Boolean(this.triggeredByEvent) }) !== false) {
      this.pressed = pressed;
    }
  }
  toggleMenu(visible) {
    var _a5, _b2, _c2;
    if (visible) {
      const { menu } = this;
      if (menu) {
        menu.owner = this;
        menu.forElement = this.element;
        if (!menu.initialConfig.minWidth) {
          menu.minWidth = this.width;
        }
        menu.align.minHeight = (_a5 = menu._minHeight) != null ? _a5 : 100;
        menu.align.minWidth = (_b2 = menu._minWidth) != null ? _b2 : 100;
        menu.show();
      }
    } else {
      (_c2 = this._menu) == null ? void 0 : _c2.hide();
    }
  }
  //endregion
};
//region Config
__publicField(Button, "$name", "Button");
__publicField(Button, "type", "button");
Button.initClass();
Button._$name = "Button";

// ../Core/lib/Core/widget/Toolbar.js
var asElementRefs = { refs: "element" };
var onCreateTwin = (overflowTwin) => overflowTwin.element.style.margin = "";
var isToolbar = (w) => w.isToolbar;
var itemScoreFn = (ent) => ent[0] + (ent[1].minifiable ? 0 : 9e9);
var twinOverrides = {
  // If the initialConfig was hidden, we must override that
  hidden: false,
  // Item must obey menu's align-items : stretch style.
  width: ""
};
var twinOverridesHorz = {
  ...twinOverrides,
  // Don't allow horizontal flex styles to apply in the vertical layout of the Menu.
  flex: ""
};
var widthAffecting = {
  flex: 1,
  hidden: 1,
  maxWidth: 1,
  minWidth: 1,
  width: 1,
  disabled: 1
  // Need to pass Toolbar.t.js t.it("Should correctly convert button w/menu when overflowed")
};
var _axisProps = [{
  box: "hbox",
  clientSizeProp: "clientWidth",
  edgeProp: "right",
  flexDir: "row",
  horizontal: true,
  max: "maxX",
  overflow: "overflowX",
  pos: "x",
  scrollSize: "scrollWidth",
  sizeProp: "width"
}, {
  box: "vbox",
  clientSizeProp: "clientHeight",
  edgeProp: "bottom",
  flexDir: "column",
  horizontal: false,
  max: "maxY",
  overflow: "overflowY",
  pos: "y",
  scrollSize: "scrollHeight",
  sizeProp: "height"
}];
var defaultRepeat = {
  delay: 0,
  startRate: 40,
  endRate: 200,
  accelerateDuration: 500
};
var nonSyncedConfigs = {
  menu: 1,
  pressed: 1
};
var _Toolbar = class _Toolbar extends Container.mixin(Toolable_default) {
  /**
   * Returns the Core.widget.Widget[] of items to hide to clear an overflow. The `visibleItems` array should be in
   * order of the `items` in the container.
   * @param {Core.widget.Widget[]} visibleItems
   * @returns {Array}
   * @private
   */
  static getEvictionList(visibleItems) {
    const ret = visibleItems.filter((it) => it.overflowable !== "none");
    ret.forEach((it, n) => ret[n] = [n, it]);
    ret.sort((a, b) => itemScoreFn(b) - itemScoreFn(a));
    return ret;
  }
  updateEnableReordering(enable) {
    var _a5;
    const me = this;
    if (enable) {
      me.dragHelper = new DragHelper({
        name: "itemReorder",
        mode: "container",
        lockY: true,
        dragThreshold: 10,
        targetSelector: ".b-widget",
        floatRootOwner: me,
        rtlSource: me,
        outerElement: me.contentElement,
        containers: [me.contentElement],
        internalListeners: {
          beforeDragStart: me.onBeforeDragStart,
          dragstart: me.onDragStart,
          itemMove: me.onItemMove,
          drop: me.onDrop,
          thisObj: me
        }
      });
    } else {
      (_a5 = me.dragHelper) == null ? void 0 : _a5.destroy();
    }
  }
  compose() {
    const me = this, { axisProps, dock } = me, endToolElementRefs = me.getEndTools(asElementRefs), startToolElementRefs = me.getStartTools(asElementRefs);
    return {
      class: {
        [`b-dock-${dock}`]: 1,
        [`b-${dock}-toolbar`]: 1,
        [`b-${axisProps.box}`]: 1
      },
      children: {
        ...startToolElementRefs,
        toolbarContent: {
          class: {
            "b-box-center": 1,
            "b-toolbar-content": 1
          }
        },
        ...endToolElementRefs
      }
    };
  }
  get axisProps() {
    return _axisProps[this.layout.horizontal ? 0 : 1];
  }
  get contentElement() {
    return this.toolbarContent;
  }
  get overflowMenuButton() {
    var _a5;
    return (_a5 = this.tools) == null ? void 0 : _a5.overflowMenuButton;
  }
  get overflowType() {
    const { overflow } = this;
    return typeof overflow === "string" ? overflow : overflow == null ? void 0 : overflow.type;
  }
  onChildAdd(item) {
    var _a5;
    super.onChildAdd(item);
    this.processAddedLeafItem(item);
    (_a5 = item.syncRotationToDock) == null ? void 0 : _a5.call(item, this.dock);
  }
  onChildRemove(item) {
    super.onChildRemove(item);
    this.syncOverflowVisibility();
  }
  processAddedLeafItem(item) {
    FunctionHelper.after(item, "onConfigChange", this.onLeafItemConfigChange, item);
    if (item.isContainer) {
      item.eachWidget((w) => this.processAddedLeafItem(w));
    }
  }
  onInternalPaint({ firstPaint }) {
    var _a5;
    (_a5 = super.onInternalPaint) == null ? void 0 : _a5.call(this, ...arguments);
    if (firstPaint) {
      this.getConfig("overflow");
    }
  }
  updateDock(dock) {
    var _a5;
    const me = this, { layout } = me, { vertical } = layout;
    layout.vertical = canonicalDock(dock)[1];
    if (!me.initialItems) {
      if (vertical !== layout.vertical) {
        me.updateOverflow(me.overflow);
      }
      for (const item of me.childItems) {
        (_a5 = item.syncRotationToDock) == null ? void 0 : _a5.call(item, dock);
      }
    }
  }
  updateOverflow(overflow, oldOverflow) {
    var _a5, _b2;
    const me = this, { axisProps, contentElement, overflowType } = me, { flexDir } = axisProps, overflowMenu = (_a5 = me.overflowMenuButton) == null ? void 0 : _a5._menu, overflowTools = {};
    if (overflowMenu) {
      if (overflow) {
        overflowMenu == null ? void 0 : overflowMenu.removeAll();
      } else {
        overflowMenu.eachWidget((overflowTwin) => {
          overflowTwin._overflowTwinOrigin.overflowTwin = null;
        });
      }
    }
    if (oldOverflow === "menu") {
      overflowTools.overflowMenuButton = null;
    } else if (oldOverflow === "scroll") {
      overflowTools.overflowScrollStart = overflowTools.overflowScrollEnd = null;
    }
    if (overflowType === "menu") {
      (_b2 = me.scrollable) == null ? void 0 : _b2.destroy();
      contentElement.style.overflow = contentElement.style.overflowX = contentElement.style.overflowY = "";
      overflowTools.overflowMenuButton = {
        cls: {
          [`b-${flexDir}-menu`]: 1
        }
      };
    } else if (overflowType === "scroll") {
      const repeat = typeof overflow === "object" && (overflow == null ? void 0 : overflow.repeat) || defaultRepeat;
      me.scrollable = {
        [axisProps.overflow]: "hidden-scroll",
        element: contentElement,
        internalListeners: {
          scroll: "onContentScroll",
          thisObj: me
        }
      };
      overflowTools.overflowScrollStart = {
        repeat,
        invertRotate: true,
        cls: {
          [`b-${flexDir}-start-scroller`]: 1
        }
      };
      overflowTools.overflowScrollEnd = {
        repeat,
        invertRotate: true,
        cls: {
          [`b-${flexDir}-end-scroller`]: 1
        }
      };
    }
    me.tools = overflowTools;
    if (overflowType) {
      contentElement.classList.add("b-overflow");
      me.monitorResize = true;
      me.syncOverflowVisibility();
    } else {
      contentElement.classList.remove("b-overflow");
      me.monitorResize = false;
    }
  }
  onContentScroll() {
    this.syncScrollerState();
  }
  onStartScrollClick() {
    this.scrollable[this.axisProps.pos] -= 2;
  }
  onEndScrollClick() {
    this.scrollable[this.axisProps.pos] += 2;
  }
  // Only called when monitorResize is true, which is only set when we have an overflow mode
  onInternalResize() {
    super.onInternalResize(...arguments);
    if (this.isPainted) {
      this.syncOverflowVisibility();
    }
  }
  syncOverflowVisibility() {
    const me = this, { overflowType, contentElement, isVisible: isVisible2 } = me, { clientSizeProp, edgeProp, sizeProp } = me.axisProps, { overflowMenuButton, overflowScrollStart, overflowScrollEnd } = me.tools, rtl = me.rtl && me.layout.horizontal, menuOverflow = overflowType === "menu", getAvailSpace = () => Math.ceil(
      contentElement[clientSizeProp] + // Since we cannot simply hide these to remove their influence, we need to add their width/height:
      (!overflowScrollStart || overflowScrollStart.hidden ? 0 : overflowScrollStart.rectangle("outer")[sizeProp]) + (!overflowScrollEnd || overflowScrollEnd.hidden ? 0 : overflowScrollEnd.rectangle("outer")[sizeProp])
    ), getContentSize = () => {
      if (visibleItems.length === 0) {
        return 0;
      }
      const rect = visibleItems[visibleItems.length - 1].rectangle(contentElement);
      return Math.floor(rtl ? contentElement[clientSizeProp] - rect.left : rect[edgeProp]);
    };
    let availableSpace, contentSize, eviction, evictionList, it, itemSize, minifiable, minifiables, minified, overflowable, overflowItems, visibleItems;
    if (!isVisible2 || !overflowType || me.items.length === 0) {
      return;
    }
    me.inSyncOverflowVisibility = true;
    overflowMenuButton == null ? void 0 : overflowMenuButton.hide();
    me.eachWidget((item, control) => {
      minifiable = item.minifiable;
      overflowable = item.overflowable;
      overflowable = minifiable ? overflowable || minifiable : overflowable;
      if (item.floating) {
        control.down = false;
      } else {
        control.down = !overflowable;
        if (item.innerItem) {
          if (item._toolbarOverflow) {
            item.hidden = item._toolbarOverflow = false;
          }
          if (item._toolbarMinified) {
            item.minified = item._toolbarMinified = false;
          }
          if (item.isVisible) {
            minifiable && (minifiables || (minifiables = [])).push(item);
            overflowable && (visibleItems || (visibleItems = [])).push(item);
          }
        }
      }
    });
    if (visibleItems) {
      availableSpace = getAvailSpace();
      contentSize = getContentSize();
    }
    if (visibleItems && contentSize > availableSpace) {
      if (menuOverflow) {
        while (contentSize > availableSpace && (it = minifiables == null ? void 0 : minifiables.pop())) {
          itemSize = contentSize;
          it._toolbarMinified = true;
          it.minified = true;
          contentSize = getContentSize();
          itemSize -= contentSize;
          (minified || (minified = [])).push([it, itemSize]);
        }
        if (contentSize > availableSpace) {
          overflowMenuButton.show();
          availableSpace = getAvailSpace();
          evictionList = _Toolbar.getEvictionList(visibleItems);
          for (eviction of evictionList) {
            if (contentSize > availableSpace) {
              it = eviction[1];
              it._toolbarOverflowWidth = it.width;
              it._toolbarOverflow = true;
              it.hidden = true;
              visibleItems.splice(visibleItems.indexOf(it), 1);
              (overflowItems || (overflowItems = [])).push(eviction);
              contentSize = getContentSize();
            }
          }
          if (overflowItems) {
            overflowItems.sort((a, b) => a[0] - b[0]).forEach((ent, n) => overflowItems[n] = ent[1]);
            while (minified == null ? void 0 : minified.length) {
              [it, itemSize] = minified.pop();
              if (contentSize + itemSize <= availableSpace && !it._toolbarOverflow) {
                contentSize += itemSize;
                it.minified = it._toolbarMinified = false;
              }
            }
            me.syncOverflowMenuButton(overflowItems);
          } else {
            overflowMenuButton.hide();
          }
        }
      } else {
        overflowScrollEnd.show();
        overflowScrollStart.show();
        me.syncScrollerState();
      }
    } else if (!menuOverflow) {
      overflowScrollEnd == null ? void 0 : overflowScrollEnd.hide();
      overflowScrollStart == null ? void 0 : overflowScrollStart.hide();
    }
    me.inSyncOverflowVisibility = false;
  }
  syncOverflowMenuButton(overflowItems) {
    var _a5;
    const me = this, { axisProps, overflowMenuButton } = me, menu = {
      cls: "b-toolbar-overflow-menu",
      minWidth: 280,
      items: [],
      align: {
        align: axisProps.horizontal ? "t100-b100" : "r100-l100",
        axisLock: "flexible"
      }
    };
    for (const item of overflowItems) {
      me.overflowItemFilter(item) && item.addToOverflowMenu(
        menu,
        me.horizontal ? twinOverridesHorz : twinOverrides,
        onCreateTwin
      );
    }
    menu.items.forEach((it) => {
      let toolbarItem = it._overflowTwinOrigin, weight = toolbarItem.parent.indexOfChild(toolbarItem);
      if (toolbarItem.parent !== me) {
        weight = me.indexOfChild(toolbarItem.parent) + weight / 1e5;
      }
      it.weight = weight;
    });
    if ((_a5 = overflowMenuButton._menu) == null ? void 0 : _a5.isMenu) {
      const existingMenu = overflowMenuButton.menu, { toAdd, toRemove } = ArrayHelper.delta(menu.items, existingMenu.items, true);
      existingMenu.remove(toRemove);
      existingMenu.add(toAdd);
    } else {
      overflowMenuButton.menu = menu;
    }
  }
  syncScrollerState() {
    const me = this, { axisProps, scrollable } = me, { overflowScrollStart, overflowScrollEnd } = me.tools, scrollPos = scrollable[axisProps.pos], maxScrollPos = scrollable[axisProps.max];
    overflowScrollStart.disabled = !scrollPos;
    overflowScrollEnd.disabled = Math.abs(Math.ceil(scrollPos)) >= Math.abs(maxScrollPos);
  }
  overflowItemFilter(item) {
    const { filter: filter2 } = this.overflow;
    return Boolean(filter2 ? filter2.call(this, item) : DomHelper.getChildElementCount(item.element));
  }
  // Note that this is called with the thisObj of the tbar item being reconfigured.
  // It propagates the new setting into its toolbar overflow clone.
  onLeafItemConfigChange(origResult, { name, value: value2 }) {
    const item = this, toolbar = item.up(isToolbar), overflow = toolbar.hasConfig("overflow"), { overflowTwin } = item;
    if (!overflow || (toolbar == null ? void 0 : toolbar.inSyncOverflowVisibility) || name === "hidden" && value2 === item._toolbarOverflow) {
      return;
    }
    if (!widthAffecting[name]) {
      if (
        // The way fixed-width widgets manage their _width property is a bit tricky because when monitorResize
        // is in effect. The _width property holds a cached width instead of a configured one.
        item._width != null && !item.monitorResize || // The size of a widget w/flex like '1 1 auto' (ie, the flex-basis is auto) is influenced by its content
        // so config changes could affect its size. Otherwise, the widget is sized to the toolbar's content
        // element and is unaffected by other configs.
        item._flex && !String(item._flex).endsWith("auto")
      ) {
        return;
      }
    }
    if (overflowTwin && !nonSyncedConfigs[name]) {
      overflowTwin[name] = value2;
    }
    if ((toolbar == null ? void 0 : toolbar.isPainted) && item.ref !== "overflowMenuButton") {
      if (!((item.isTextField || item.isSlider || item.isCheckbox) && name === "value" && (item.containsFocus || (overflowTwin == null ? void 0 : overflowTwin.containsFocus)))) {
        toolbar.syncOverflowVisibility();
      }
    }
  }
  createWidget(widget) {
    if (widget === "->") {
      widget = {
        type: "widget",
        cls: "b-toolbar-fill"
      };
    } else if (widget === "|") {
      widget = {
        type: "widget",
        cls: "b-toolbar-separator"
      };
    } else if (typeof widget === "string") {
      widget = {
        type: "widget",
        cls: "b-toolbar-text",
        html: widget
      };
    }
    const result = super.createWidget(widget);
    if (this.widgetCls) {
      result.element.classList.add(this.widgetCls);
    }
    return result;
  }
  //region Events (drop)
  onBeforeDragStart({ context, event }) {
    const item = context.item = this.items[context.element.dataset.itemIndex];
    context.oldIndex = Number.parseInt(item.element.dataset.itemIndex, 10);
    return this.trigger("beforeItemDragStart", { item, domEvent: event });
  }
  onDragStart({ context, event }) {
    const { item } = context;
    context.dragProxy.style.backgroundColor = globalThis.getComputedStyle(this.element).backgroundColor;
    this.trigger("itemDragStart", { item, domEvent: event });
  }
  onItemMove({ context, event }) {
    const { item } = context, index = Array.from(item.element.parentElement.children).indexOf(item.element);
    this.insert(item, index);
    this.trigger("itemDragMove", { item, domEvent: event });
  }
  /**
   * Handle drop
   * @private
   */
  onDrop({ context, event }) {
    const me = this, { item, valid } = context;
    me.trigger("itemDrop", { item, domEvent: event, valid });
  }
  //endregion
  doDestroy() {
    var _a5;
    (_a5 = this.dragHelper) == null ? void 0 : _a5.destroy();
    super.doDestroy();
  }
};
__publicField(_Toolbar, "$name", "Toolbar");
__publicField(_Toolbar, "type", "toolbar");
__publicField(_Toolbar, "delayable", {
  syncOverflowVisibility: {
    type: "raf",
    cancelOutstanding: true
  }
  // && 50   // restore the "&& 50" here to help when debugging syncOverflowVisibility
});
__publicField(_Toolbar, "configurable", {
  role: "toolbar",
  defaultType: "button",
  dock: "top",
  layout: {
    type: "box"
  },
  /**
   * Set to `true` to allow users to reorder items in this toolbar using drag-drop
   * @prp {Boolean}
   */
  enableReordering: null,
  /**
   * An object containing typed child widget config objects or {@link Core/widget/Widget Widgets}. Can also be
   * specified as an array.
   *
   * If configured as an Object, the property names are used as the child component's
   * {@link Core.widget.Widget#config-ref} name, and the value is the child component's config object.
   *
   * ```javascript
   *  new Toolbar({
   *      appendTo : document.body
   *      items    : {
   *          button : {
   *              type : 'button',
   *              onClick() { ... }
   *          }
   *      }
   *  })
   * ```
   *
   * Some special items can also be added:
   *
   * - `->` Pushes following items to the other side
   * - `|` Adds a vertical separator
   *
   * ```javascript
   *  new Toolbar({
   *      appendTo : document.body
   *      items    : {
   *          button : {
   *              type : 'button',
   *              text : 'Click me',
   *              onClick() { ... }
   *          },
   *          separator : '|',
   *          name : {
   *              type : 'textfield',
   *              label : 'Enter name'
   *          },
   *          // Align following items to the end of the toolbar
   *          spacer : '->',
   *          saveButton : {
   *              type : 'button',
   *              text : 'Save',
   *              onClick() { ... }
   *          }
   *      }
   *  })
   * ```
   *
   * @config {Object<String,ToolbarItems|Boolean|null>|ToolbarItems[]|Core.widget.Widget[]} items
   * @category Content
   */
  /**
   * How this Toolbar should deal with items that overflow its main axis.
   *
   * Values may be:
   * - `'menu'` A button with a menu is shown and the menu contains the overflowing items.
   * - `'scroll'` The items overflow and mey be scrolled into view using the mouse or scroll buttons.
   * - `null` Disable overflow handling
   *
   * When mode is `'menu'`, clones of overflowing toolbar item are created and added to a Menu. Any config
   * changes to the original toolbar item are propagated to the menu's clone, so disabling a toolbar
   * item will make the clone in the menu disabled.
   *
   * The clone of an input field will propagate its `value` changes back to the original. The
   * overflow button, its menu, and the clones should not be accessed or manipulated by application code.
   *
   * Note that cloned items will be allocated a unique, generated ID because all IDs must be unique,
   * so CSS targeting an element ID will not apply to a clone in the overflow menu.
   *
   * Values may also be specified in object form containing the following properties:
   * @config {String|Object|null} overflow
   * @property {'scroll'|'menu'} overflow.type `'scroll'` or `'menu'`
   * @property {ClickRepeaterConfig} overflow.repeat A config object to reconfigure the
   * {@link Core.util.ClickRepeater} which controls auto repeat speed when holding down the scroll buttons
   * when `type` is `'scroll'`
   * @property {Function} overflow.filter A filter function which may return a falsy value to prevent toolbar
   * items from being cloned into the overflow menu.
   * @default 'menu'
   */
  overflow: {
    // Wait until first paint to evaluate so that we can read our CSS style.
    // Set to null on destroy which destroys the overflow Tools and Scroller.
    $config: ["lazy", "nullify"],
    value: {
      type: "menu"
    }
  },
  toolDefaults: {
    overflowMenuButton: {
      type: "button",
      hidden: true,
      icon: "b-icon-menu",
      menuIcon: null,
      ariaLabel: "L{Toolbar.showOverflowingItems}",
      defaultCls: {
        "b-overflow-button": 1
      }
    },
    overflowScrollEnd: {
      handler: "up.onEndScrollClick",
      hidden: true,
      ariaLabel: "L{Toolbar.scrollForMore}",
      defaultCls: {
        "b-icon-angle-right": 1,
        "b-overflow-button": 1,
        "b-icon": 1
      }
    },
    overflowScrollStart: {
      align: "start",
      handler: "up.onStartScrollClick",
      hidden: true,
      ariaLabel: "L{Toolbar.scrollForMore}",
      defaultCls: {
        "b-icon-angle-left": 1,
        "b-overflow-button": 1,
        "b-icon": 1
      }
    }
  },
  /**
   * Custom CSS class to add to toolbar widgets
   * @config {String}
   * @category CSS
   */
  widgetCls: null,
  /**
   * Determines if the toolbars read-only state should be controlled by its parent.
   *
   * When set to `true`, setting a parent container to read-only will not affect the widget. When set to
   * `false`, it will affect the widget.
   *
   * @category Misc
   * @config {Boolean}
   * @default
   */
  ignoreParentReadOnly: true
});
__publicField(_Toolbar, "prototypeProperties", {
  flexRowCls: "b-hbox",
  flexColCls: "b-vbox"
});
var Toolbar = _Toolbar;
Toolbar.initClass();
Toolbar._$name = "Toolbar";

// ../Core/lib/Core/widget/Panel.js
var isTabbable = (e) => !e.classList.contains("b-focus-trap") && !e.classList.contains("b-hidden") && DomHelper.getFocusability(e).focusable ? DomHelper.NodeFilter.FILTER_ACCEPT : DomHelper.NodeFilter.FILTER_SKIP;
var emptyArray7 = [];
var emptyObject10 = {};
var emptySplit = [emptyArray7, emptyArray7];
var finishBodyWrap = (config, classes, final) => {
  const { vertical } = config;
  delete config.vertical;
  return {
    ...config,
    class: {
      ...classes,
      [`b-${vertical ? "v" : "h"}box`]: 1,
      "b-box-center": 1,
      "b-panel-bar-wrap": !final
    }
  };
};
var wrapBody = (inner, bodyWrapTag, vertical = false) => {
  const wrap = {
    vertical,
    children: inner ? [inner] : []
  };
  if (bodyWrapTag) {
    wrap.tag = bodyWrapTag;
  }
  return wrap;
};
var setCls = (elOrConfig, cls) => {
  if (elOrConfig == null ? void 0 : elOrConfig.classList) {
    elOrConfig == null ? void 0 : elOrConfig.classList.add(cls);
  } else if (elOrConfig == null ? void 0 : elOrConfig.class) {
    if (typeof elOrConfig.class === "string") {
      elOrConfig.class = { [elOrConfig.class]: 1 };
    }
    elOrConfig.class[cls] = 1;
  }
};
var barConfigs = {
  dock: 1,
  hidden: 1,
  weight: 1
};
var dockDirection = {
  //       [vertical, before]
  top: [true, true],
  bottom: [true, false],
  left: [false, true],
  right: [false, false]
};
var headerDock = {
  header: 1,
  "pre-header": 1
};
var directionByDock2 = {
  top: "up",
  right: "right",
  bottom: "down",
  left: "left"
};
var _Panel = class _Panel extends Container.mixin(State_default, Toolable_default) {
  static get configurable() {
    return {
      localizableProperties: ["title"],
      /**
       * Custom CSS class name suffixes to apply to the elements rendered by this widget. This may be specified
       * as a space separated string, an array of strings, or as an object in which property names with truthy
       * values are used as the class names.
       *
       * The panel supports a few special UIs, such as `plain` rendition where toolbars and header have no
       * background/borders, and `toolbar`.
       *
       * For example, consider a `Panel` with a `ui` config like so:
       *
       * ```javascript
       *  new Panel({
       *      text : 'OK',
       *      ui   : 'light'
       *  });
       * ```
       *
       * This will apply the CSS class `'b-panel-ui-light'` to the main element of the panel as well as its many
       * child elements. This allows simpler CSS selectors to match the child elements of this particular panel
       * UI:
       *
       * ```css
       *  .b-panel-content.b-panel-ui-light {
       *      background-color : #eee;
       *  }
       * ```
       *
       * Using the {@link #config-cls cls config} would make matching the content element more complex, and in
       * the presence of {@link Core.widget.Panel#config-strips docked items} and nested panels, impossible to
       * target accurately.
       *
       * @config {'plain'|'toolbar'|String|Object} ui
       * @category CSS
       */
      /**
       * Controls whether the panel is collapsed (the body of the panel is hidden while only the header is
       * visible). Only valid if the panel is {@link #config-collapsible}.
       * @prp {Boolean}
       * @category Layout
       */
      collapsed: {
        value: null,
        $config: null,
        default: false
      },
      /**
       * This config enables collapsibility for the panel. See {@link #config-collapsed}.
       *
       * For example:
       *
       * ```javascript
       *      {
       *          type        : 'panel',
       *          collapsible : true
       *      }
       * ```
       *
       * This is managed by an instance of {@link Core.widget.panel.PanelCollapser} which can be configured if an
       * object is passed for this config property:
       *
       * ```javascript
       *      {
       *          type        : 'panel',
       *          collapsible : {
       *              direction : 'left'
       *          }
       *      }
       * ```
       *
       * The config object form can contain a `type` property to specify the type of collapse the panel will use.
       * This property can be one of the following:
       *
       * - `'inline'` (see {@link Core.widget.panel.PanelCollapser})
       * - `'overlay'` (see {@link Core.widget.panel.PanelCollapserOverlay})
       *
       * The default `direction` property is inferred from the position of the Panel in a flexbox layout.
       *
       * If the Panel is the last child of a flexbox container, the `direction` is `'right'` for a horizontal
       * flexbox layout and `'down'` for a vertical layout.
       *
       * All other Panels in a flexbox container have the `direction` set to `'left'` for a horizontal layout and
       * `'up'` for a vertical layout.
       *
       * @config {Boolean|PanelCollapserConfig|PanelCollapserOverlayConfig}
       * @category Layout
       */
      collapsible: {
        value: null,
        $config: "nullify"
      },
      /**
       * Custom CSS classes to add to the panel's body element.
       *
       * May be specified as a space separated string, or as an object in which property names
       * with truthy values are used as the class names:
       *
       * ```javascript
       *  bodyCls : {
       *      'b-my-class'     : 1,
       *      [this.extraCls]  : 1,
       *      [this.activeCls] : this.isActive
       *  }
       * ```
       *
       * @config {String|Object}
       * @category CSS
       */
      bodyCls: {
        $config: {
          merge: "classList"
        },
        value: null
      },
      bodyTag: null,
      bodyWrapTag: null,
      /**
       * By default, tabbing within a Panel is not contained, ie you can TAB out of the Panel
       * forwards or backwards.
       * Configure this as `true` to disallow tabbing out of the Panel, and make tabbing circular within this Panel.
       * @config {Boolean}
       * @default false
       * @category Content
       */
      trapFocus: null,
      /**
       * A title to display in the header or owning TabPanel. Causes creation and docking of a header
       * to the top if no header is configured.
       *
       * If a header has been disabled by configuring the {@link #config-header}as `false`, setting it will have
       * no effect. {@note}The title is not HTML-encoded{/@note}
       *
       * {@note}The title is not HTML-encoded{/@note}
       * @default
       * @prp {String}
       * @category Misc
       */
      title: null,
      /**
       * A config {@link PanelHeader object} for the panel's header or a string in place of a `title`.
       *
       * Configuring this as `false` explicitly removes the header bar, overriding any
       * {@link #config-tools} or {@link #config-title} configs.
       * @default
       * @config {String|Boolean|PanelHeader}
       * @category Content
       */
      header: null,
      stateful: ["collapsed"],
      /**
       * An object containing config defaults for corresponding {@link #config-strips} objects with a matching name.
       *
       * By default, this object contains the keys `'bbar'` and `'tbar'` to provide default config values for the
       * {@link #config-bbar} and {@link #config-tbar} configs.
       *
       * This object also contains a key named `'*'` with default config properties to apply to all strips. This
       * object provides the default `type` (`'toolbar') and {@link Core.widget.Widget#config-dock} (`'top'`)
       * property for strips.
       * @config {Object} stripDefaults
       * @internal
       * @category Content
       */
      stripDefaults: {
        "*": {
          type: "toolbar",
          dock: "top"
        },
        bbar: {
          dock: "bottom",
          weight: -1e3
        },
        tbar: {
          weight: -1e3
        }
      },
      /**
       * An object containing widgets keyed by name. By default (when no `type` is given), strips are
       * {@link Core.widget.Toolbar toolbars}. If you want to pass an array, you can use
       * the toolbar's {@link Core.widget.Container#config-items}.
       *
       * The {@link #config-bbar} and {@link #config-tbar} configs are shortcuts for adding toolbars to the
       * panel's `strips`.
       *
       * Strips are arranged based on their {@link Core.widget.Widget#config-dock} and
       * {@link Core.widget.Widget#config-weight} configs.
       *
       * For widgets using a `dock` of `'top'`, `'bottom'`, `'left'`, `'right'`, `'start'` or `'end'`(an "edge
       * strip"), the higher the `weight` assigned to a widget, the closer that widget will be to the panel body.
       *
       * For widgets with `'header'` or `'pre-header'` for `dock` (a "header strip"), higher `weight` values
       * cause the widget to be placed closer to the panel's title.
       *
       * ```javascript
       *  new Panel({
       *      title : 'Test',
       *      html  : 'Panel strip test',
       *      strips : {
       *          left : {
       *              dock  : 'left'
       *              items : {
       *                  go : {
       *                      text : 'Go'
       *                  }
       *              }
       *          }
       *      }
       *  });
       *
       * // Pass an array to tbar
       * strips : {
       *     tbar : {
       *         items: {
       *             addButton : {
       *                 type : 'button',
       *                 text : 'Add column',
       *                 icon : 'b-fa-plus',
       *                 onAction : ({ source }) => addColumn(source.element)
       *             },
       *             // consider 'getCurrentDate()` a custom function
       *             today : `Today is ${getCurrentDate()}`
       *         }
       *     }
       * }
       * ```
       *
       * @config {Object<String,ContainerItemConfig>} strips
       * @category Content
       */
      strips: {
        value: null,
        $config: "nullify"
      },
      toolDefaults: {
        close: {
          weight: -1e3
        },
        collapse: {
          weight: -990
        }
      },
      /**
       * Config object of a footer. May contain a `dock`, `html` and a `cls` property. A footer is not a widget,
       * but rather plain HTML that follows the last element of the panel's body and {@link #config-strips}.
       *
       * The `dock` property may be `top`, `right`, `bottom`, `left`, `start` or `end`
       *
       * @config {Object|String}
       * @property {'top'|'right'|'bottom'|'left'|'start'|'end'} dock Where to dock
       * @property {String} html Html to populate the footer with
       * @property {String} cls CSS class to add to the footer
       * @default
       * @category Content
       */
      footer: null,
      /**
       * This config is used with {@link Core.widget.panel.PanelCollapserOverlay} to programmatically control the
       * visibility of the panel's body. In this mode of collapse, the body of a collapsed panel is a floating
       * overlay. Setting this config to `true` will show this element, while `false` will hide it.
       * @config {Boolean}
       * @private
       */
      revealed: null,
      /**
       * The tool Widgets as specified by the {@link #config-tools} configuration
       * (and the {@link Core.widget.Popup#config-closable} configuration in the Popup subclass).
       * Each is a {@link Core.widget.Widget} instance which may be hidden, shown and observed and styled
       * just like any other widget.
       *
       * ```javascript
       * panel.tools.add = {
       *     cls : 'b-fa b-fa-plus',
       *     handler() {
       *         // Clicked the tool
       *     }
       * }
       * ```
       *
       * @member {Object<String,Core.widget.Tool>} tools
       * @accepts {Object<String,Core.widget.Tool|ToolConfig>|null}
       */
      /**
       * The {@link Core.widget.Tool tools} to add either before or after the `title` in the Panel header. Each
       * property name is the reference by which an instantiated tool may be retrieved from the live
       * `{@link Core.widget.mixin.Toolable#property-tools}` property.
       *
       * ```javascript
       * new Panel({
       *     ...
       *     tools : {
       *         add : {
       *             cls : 'b-fa b-fa-plus',
       *             handler() {
       *                 // Clicked the tool
       *             }
       *         }
       *     }
       * });
       * ```
       *
       * @config {Object<string,ToolConfig>|null} tools
       * @category Content
       */
      /**
       * Get toolbar {@link Core.widget.Toolbar} docked to the top of the panel
       * @member {Core.widget.Toolbar} tbar
       * @readonly
       * @category Content
       */
      /**
       * A Config object representing the configuration of a {@link Core.widget.Toolbar},
       * or array of config objects representing the child items of a Toolbar.
       * This creates a toolbar docked to the top of the panel immediately below the header.
       *
       * To add toolbars not docked to the top, see {@link #config-bbar} and {@link #config-strips}.
       *
       * ```javascript
       * // Text only
       * tbar : ['Project Timeline Overview', '->', 'Key Milestones & Deadlines'],
       *
       * // Buttons
       * tbar : [
       *    {
       *       type     : 'button',
       *       ref      : 'addButton',
       *       text     : 'Add column',
       *       icon     : 'b-fa-plus',
       *       tooltip  : 'Add new column',
       *       onAction : ({ source }) => addColumn(source.element) // add new column
       *   },
       *   {
       *       type     : 'button',
       *       ref      : 'removeButton',
       *       text     : 'Remove last',
       *       icon     : 'b-fa-minus',
       *       tooltip  : 'Remove last column',
       *       onAction : () => grid.columns.count > 1 && grid.columns.last.remove() // remove last column
       *   }
       * ]
       *
       * // ToolbarConfig
       * tbar : {
       *     height : '4em',
       *     items  : {
       *         button1 : { text : 'A button', icon : 'b-fa b-fa-plus' },
       *         button2 : { text : 'Right button 1', icon : 'b-fa b-fa-trash', style : 'margin-inline-start:auto' },
       *         button3 : { icon : 'b-fa b-fa-gear' }
       *     }
       * }
       * ```
       *
       * @config {Array<ContainerItemConfig|String>|ToolbarConfig|null}
       * @typings {Array<ContainerItemConfig|String>|ToolbarConfig|PagingToolbarConfig|null}
       * @category Content
       */
      tbar: null,
      /**
       * Get toolbar {@link Core.widget.Toolbar} docked to the bottom of the panel
       * @member {Core.widget.Toolbar} bbar
       * @readonly
       * @category Content
       */
      /**
       * A Config object representing the configuration of a {@link Core.widget.Toolbar},
       * or array of config objects representing the child items of a Toolbar. Another way to add a bbar is to use {@link #config-strips}.
       *
       * ```javascript
       * // Text only
       * bbar : [`Total number of students: ${totalStds}`, '->', `Passed Students: ${passedStds}`],
       *
       * // Buttons
       * bbar : [
       *    {
       *       type     : 'button',
       *       ref      : 'addButton',
       *       text     : 'Add column',
       *       icon     : 'b-fa-plus',
       *       tooltip  : 'Add new column',
       *       onAction : ({ source }) => addColumn(source.element) // add new column
       *   },
       *   {
       *       type     : 'button',
       *       ref      : 'removeButton',
       *       text     : 'Remove last',
       *       icon     : 'b-fa-minus',
       *       tooltip  : 'Remove last column',
       *       onAction : () => grid.columns.count > 1 && grid.columns.last.remove() // remove last column
       *   }
       * ]
       *
       * // ToolbarConfig
       * bbar : {
       *     height : '4em',
       *     items  : {
       *         button1 : { text : 'A button', icon : 'b-fa b-fa-plus' },
       *         button2 : { text : 'Right button 1', icon : 'b-fa b-fa-trash', style : 'margin-inline-start:auto' },
       *         button3 : { icon : 'b-fa b-fa-gear' }
       *     }
       * }
       * ```
       *
       * This creates a toolbar docked to the bottom of the panel immediately above the footer.
       * @config {Array<ContainerItemConfig|String>|ToolbarConfig|null}
       * @typings {Array<ContainerItemConfig|String>|ToolbarConfig|PagingToolbarConfig|null}
       * @category Content
       */
      bbar: null,
      role: null,
      /**
       * Make this Panel a docked drawer which slides out from one side of the browser viewport by default.
       *
       * If this Panel is a child of another widget, the drawer will slide out from the side of the parent widget.
       *
       * By default, it floats above the content of its host. If you want the drawer to be inline within the host,
       * consuming content space in the host, set the `drawer` `inline` property to `true`.
       *
       * {@inlineexample Core/widget/PanelDrawer.js}
       *
       * @config {Boolean|Object}
       * @param {'start'|'left'|'end'|'right'|'top'|'bottom'} [side='end'] The side of the viewport to dock the drawer to.
       * - `'start'` means the `inline-start` side.
       * - `'end'` means the `inline-end` side.
       * @param {String|Number} [size='30em'] The size of the drawer in its collapsible axis.
       * @param {Boolean} [inline=false] If using the {@link #config-appendTo} config to place the drawer inside a host
       * widget, set may be set to `true` to make the drawer inline within that host. Note that, if using this, the
       * layout of the host element must have `flex-direction` set appropriately.
       */
      drawer: {
        $config: "lazy",
        value: null
      }
    };
  }
  //endregion
  /**
   * A header {@link #config-tools tool} has been clicked.
   * @event toolClick
   * @param {Core.widget.Tool} source - This Panel.
   * @param {Core.widget.Tool} tool - The tool which is being clicked.
   */
  /**
   * Fires when a Panel is collapsed using the {@link Core.widget.Panel#config-collapsible} setting.
   * @event collapse
   * @param {Core.widget.Panel} source - This Panel.
   */
  /**
   * Fires when a Panel is expanded using the {@link Core.widget.Panel#config-collapsible} setting.
   * @event expand
   * @param {Core.widget.Panel} source - This Panel.
   */
  //region Composition
  get bodyWrapRootElement() {
    return this[this._bodyWrapRootName];
  }
  changeFooter(footer) {
    if (footer) {
      if (footer.dock === "start") {
        footer.dock = "left";
      } else if (footer.dock === "end") {
        footer.dock = "right";
      }
    }
    return footer;
  }
  compose() {
    var _a5, _b2;
    const me = this, { collapsible, focusable, revealed, tools, role, floating } = me, header = me.composeHeader(), afters = me.getPositionableItems("after", true), befores = me.getPositionableItems("before", true), horz = (header == null ? void 0 : header.class["b-dock-left"]) || (header == null ? void 0 : header.class["b-dock-right"]), titleId = (_b2 = (_a5 = header == null ? void 0 : header.children) == null ? void 0 : _a5[0]) == null ? void 0 : _b2.id;
    let body = me.composeBody(), key = "bodyWrapElement";
    if (collapsible) {
      [key, body] = collapsible.wrapCollapser(key, body);
    }
    me._bodyWrapRootName = key;
    const result = {
      role: role && me._ariaElement === "element" ? role : floating ? "dialog" : "region",
      // Floaters have to be focusable using pointer events, but *not* tabbable, so -1
      // Otherwise, focusable means tabbable so that would be 0
      tabIndex: (floating || me.peekConfig("positioned")) && focusable !== false ? -1 : focusable ? 0 : null,
      class: {
        [`b-panel-collapsible-${collapsible == null ? void 0 : collapsible.type}`]: collapsible,
        [`b-panel-collapse-${collapsible == null ? void 0 : collapsible.collapseDir}`]: collapsible,
        [`b-${horz ? "h" : "v"}box`]: 1,
        "b-panel-collapsible": collapsible,
        "b-panel-has-header": header,
        "b-panel-has-tools": tools ? 1 : 0,
        "b-panel-overlay-revealed": revealed
      },
      children: {
        topFocusTrap: {
          "aria-hidden": true,
          tabIndex: 0,
          class: {
            "b-focus-trap": 1,
            "b-top-focus-trap": 1
          }
        },
        ...befores,
        // Note: we always put header before bodyWrap since it is likely (though untested) to be better for
        // a11y. We use flexbox order to make the right/bottom docking appear correct but it is likely that
        // the DOM order of the <header> element vs (optional) <footer> is important to screen readers.
        headerElement: header,
        [key]: body,
        ...afters,
        bottomFocusTrap: {
          "aria-hidden": true,
          tabIndex: 0,
          class: {
            "b-focus-trap": 1,
            "b-end-focus-trap": 1
          }
        }
      }
    };
    if (titleId) {
      result["aria-labelledby"] = titleId;
    }
    return result;
  }
  updateElement() {
    this.getConfig("drawer");
    super.updateElement(...arguments);
  }
  updateDrawer(drawer) {
    var _a5;
    let {
      side = "end",
      inline
    } = drawer;
    const me = this, {
      element,
      rtl
    } = me, appendTo = me.getRenderContext()[0] || (inline ? document.body : null), posType = me.parent || appendTo && !inline ? "positioned" : "floating";
    side = side === "start" ? rtl ? "right" : "left" : side === "end" ? rtl ? "left" : "right" : side;
    me.updateConfiguration({
      // Clear any configs which are not valid for a drawer
      // A Popup may be configured as a drawer, and Popups have these by default.
      forElement: null,
      closable: false,
      align: null,
      autoShow: false,
      monitorResize: false,
      centered: false,
      anchor: false,
      hidden: false,
      draggable: false,
      appendTo: appendTo || ((_a5 = me.owner) == null ? void 0 : _a5.floatRoot) || me.floatRoot,
      hideMode: "collapse",
      trapFocus: true,
      [posType]: !inline,
      collapsed: typeof drawer === "object" && "collapsed" in drawer ? drawer.collapsed : true,
      collapsible: {
        ...drawer,
        type: inline ? "inline" : "overlay",
        direction: directionByDock2[side || "end"]
      }
    });
    element.classList.add("b-drawer-panel");
    element.classList.remove("b-popup");
    element.style.setProperty("--b-drawer-size", DomHelper.setLength(drawer == null ? void 0 : drawer.size));
    me.ion({
      thisObj: me,
      reveal: () => me.trigger("show"),
      unreveal: () => me.trigger("hide")
    });
  }
  get isVisible() {
    if (this.drawer) {
      return !this.collapsible.collapsed || this.revealed;
    } else
      return super.isVisible;
  }
  // Override for when this Panel is a drawer. Hide collapses/unreveals the drawer.
  hide() {
    const me = this;
    if (me.drawer) {
      if (me.trigger("beforeHide") !== false) {
        const result = me.collapsible.type === "inline" ? me.collapse() : me.collapsible.toggleReveal(false);
        me.trigger("hide");
        return result;
      }
    } else {
      return super.hide(...arguments);
    }
  }
  // Override for when this Panel is a drawer. Show expands/reveals the drawer.
  show() {
    const me = this;
    if (me.drawer) {
      if (me.trigger("beforeShow") !== false) {
        const result = me.collapsible.type === "inline" ? me.expand() : me.collapsible.toggleReveal(true);
        me.afterShow();
        me.focus();
        return result;
      }
    } else {
      return super.show(...arguments);
    }
  }
  showBy() {
    if (this.drawer) {
      return this.show();
    }
    return super.showBy(...arguments);
  }
  composeBody() {
    var _a5, _b2;
    const me = this, { bodyCls, bodyConfig, bodyWrapTag, footer, uiClassList } = me, strips = ObjectHelper.values(me.strips, (k, v) => !dockDirection[v == null ? void 0 : v.dock]).sort(me.byWeightSortFn), innermostStrips = {
      top: null,
      right: null,
      bottom: null,
      left: null
    };
    let bar, before, dock, i, name, vertical, wrap;
    if (footer) {
      dock = footer.dock || "bottom";
      strips.unshift({
        dock,
        element: {
          tag: "footer",
          reference: "footerElement",
          html: typeof footer === "string" ? footer : footer.html,
          class: {
            ...uiClassList,
            [`b-dock-${dock}`]: 1,
            [`${footer.cls || ""}`]: 1
          }
        }
      });
    }
    if (bodyCls) {
      if (!bodyConfig[name = "className"]) {
        name = "class";
      }
      bodyConfig[name] = new DomClassList(bodyConfig[name]).assign(bodyCls);
    }
    for (i = strips.length; i-- > 0; ) {
      bar = strips[i];
      [vertical, before] = dockDirection[bar.dock];
      if (!wrap) {
        wrap = wrapBody(bodyConfig, bodyWrapTag, vertical);
      } else if (wrap.vertical !== vertical) {
        wrap = wrapBody(finishBodyWrap(wrap, uiClassList), "", vertical);
      }
      wrap.children[before ? "unshift" : "push"](bar.element);
      innermostStrips[bar.dock] = bar;
    }
    setCls(innermostStrips.top, "b-innermost");
    setCls(innermostStrips.right, "b-innermost");
    setCls(innermostStrips.bottom, "b-innermost");
    setCls(innermostStrips.left, "b-innermost");
    const body = finishBodyWrap(wrap || wrapBody(bodyConfig, bodyWrapTag), uiClassList, true);
    body.class[`${(_a5 = me.layout) == null ? void 0 : _a5.containerCls}-panel`] = Boolean((_b2 = me.layout) == null ? void 0 : _b2.containerCls);
    body.class["b-panel-body-wrap"] = 1;
    body.class[`b-${me.$$name.toLowerCase()}-body-wrap`] = 1;
    return body;
  }
  get hasHeader() {
    if (this.header === false) {
      return false;
    }
    const { header, title, tools, parent } = this, hasVisibleTools = this.maximizable || Object.values(tools || {}).some((tool) => !tool.hidden);
    return header || !(parent == null ? void 0 : parent.suppressChildHeaders) && (title || hasVisibleTools);
  }
  get rootUiClass() {
    return _Panel;
  }
  changeHeader(header) {
    return typeof header === "string" ? { title: header } : header;
  }
  composeHeader(force) {
    var _a5;
    const me = this;
    if (!me.hasHeader && !force) {
      return;
    }
    const header = me.header || {}, dock = header.dock || "top", [before, after] = me.splitHeaderItems({ as: "element", dock }), classes = me.$meta.hierarchy, title = me.composeTitle(header), cls = new DomClassList({
      [`b-dock-${dock}`]: 1,
      ...me.uiClassList
    }, header.cls);
    let i, name;
    for (i = classes.indexOf(_Panel); i < classes.length; ++i) {
      name = classes[i].$$name;
      if (name !== "Grid") {
        cls[`b-${name.toLowerCase()}-header`] = 1;
      }
    }
    const headerConfig = {
      ...header,
      tag: "header",
      class: cls,
      children: [
        ...before,
        title,
        ...after
      ]
    };
    return ((_a5 = me.collapsible) == null ? void 0 : _a5.composeHeader(headerConfig)) || headerConfig;
  }
  composeTitle(header) {
    var _a5;
    const title = this.title || header.title, titleConfig = {
      reference: "titleElement",
      id: `${this.id}-panel-title`,
      "aria-live": "polite",
      class: {
        [`b-align-${header.titleAlign || "start"}`]: 1,
        "b-header-title": 1,
        ...this.uiClassList
      }
    };
    if (title) {
      if (ObjectHelper.isObject(title)) {
        ObjectHelper.merge(titleConfig, title);
      } else {
        const { iconCls } = header;
        if (iconCls) {
          titleConfig.children = [{ tag: "i", class: "b-panel-title-icon " + iconCls }, title];
        } else {
          titleConfig.html = title;
        }
      }
    }
    return ((_a5 = this.collapsible) == null ? void 0 : _a5.composeTitle(titleConfig)) || titleConfig;
  }
  // Needed to make title go through recompose
  updateTitle() {
  }
  afterRecompose() {
    var _a5;
    super.afterRecompose();
    const me = this, { headerElement } = me;
    (_a5 = me._headerClickDetacher) == null ? void 0 : _a5.call(me);
    me._headerClickDetacher = headerElement && EventHelper.on({
      element: headerElement,
      // Click might have lead to panel being destroyed (clicking close tool with `closeAction : 'destroy'`)
      click: (ev) => {
        var _a6;
        return (_a6 = me.trigger) == null ? void 0 : _a6.call(me, "panelHeaderClick", { event: ev });
      }
    });
  }
  onPanelHeaderClick(info) {
    var _a5;
    (_a5 = this.collapsible) == null ? void 0 : _a5.onPanelHeaderClick(info);
  }
  onInternalPaint() {
    var _a5;
    super.onInternalPaint(...arguments);
    (_a5 = this.collapsible) == null ? void 0 : _a5.onPanelPaint(this);
  }
  splitHeaderItems({ as, dock, alt: alt2 } = emptyObject10) {
    var _a5, _b2, _c2, _d;
    const me = this, asElement = as === "element", { collapsed } = me, endTools = me.getEndTools({ alt: alt2 }), startTools = me.getStartTools({ alt: alt2 }), strips = ObjectHelper.values(
      me.strips,
      (k, v) => !headerDock[v == null ? void 0 : v.dock] && v.isCollapsified({ collapsed, alt: alt2 })
    );
    let ret = emptySplit, after, before, i;
    if (strips.length + endTools.length + startTools.length) {
      ret = [
        // the problem w/mixing tools and strips is the strip weight needs to do two jobs (one when docked
        // in the body and one when docked in the header)
        before = [
          ...startTools,
          ...strips.filter((e) => e.dock === "pre-header").sort(me.byWeightSortFn)
        ],
        after = [
          ...strips.filter((e) => e.dock === "header").sort(me.byWeightReverseSortFn),
          ...endTools
        ]
      ];
      for (i = 0; i < before.length; ++i) {
        dock && ((_b2 = (_a5 = before[i]).syncRotationToDock) == null ? void 0 : _b2.call(_a5, dock));
        if (asElement) {
          before[i] = before[i].element;
        }
      }
      for (i = 0; i < after.length; ++i) {
        dock && ((_d = (_c2 = after[i]).syncRotationToDock) == null ? void 0 : _d.call(_c2, dock));
        if (asElement) {
          after[i] = after[i].element;
        }
      }
    }
    return ret;
  }
  set bodyConfig(bodyConfig) {
    this._bodyConfig = bodyConfig;
  }
  get bodyConfig() {
    const me = this, { bodyTag } = me, result = ObjectHelper.merge({
      reference: "bodyElement",
      className: {
        ...me.getStaticWidgetClasses(_Panel, "-content"),
        "b-box-center": 1,
        "b-text-content": me.textContent && me.hasNoChildren
      }
    }, me._bodyConfig);
    if (bodyTag) {
      result.tag = bodyTag;
    }
    return result;
  }
  //endregion
  //region Configs
  changeBodyCls(cls) {
    return DomClassList.from(cls);
  }
  changeTbar(bar) {
    this.getConfig("strips");
    this.strips = {
      tbar: bar
    };
    return this.strips.tbar;
  }
  changeBbar(bar) {
    this.getConfig("strips");
    this.strips = {
      bbar: bar
    };
    return this.strips.bbar;
  }
  // Override to iterate docked Toolbars in the correct order around contained widgets.
  get childItems() {
    var _a5;
    const me = this, strips = ObjectHelper.values(me.strips, (k, v) => !dockDirection[v == null ? void 0 : v.dock]).sort(me.byWeightSortFn), [before, after] = me.splitHeaderItems(), [before2, after2] = ((_a5 = me.collapsible) == null ? void 0 : _a5.splitHeaderItems()) || emptySplit;
    return [
      ...before,
      ...before2,
      ...after,
      ...after2,
      ...strips.filter((b) => dockDirection[b.dock][1]),
      // the "before" strips come before the items
      ...me.items,
      ...strips.filter((b) => !dockDirection[b.dock][1]).reverse()
    ];
  }
  get childItemsOrderedByRelevance() {
    var _a5;
    const me = this, strips = ObjectHelper.values(me.strips, (k, v) => !dockDirection[v == null ? void 0 : v.dock]).sort(me.byWeightSortFn), [before, after] = me.splitHeaderItems(), [before2, after2] = ((_a5 = me.collapsible) == null ? void 0 : _a5.splitHeaderItems()) || emptySplit;
    return [
      ...me.items,
      ...strips.filter((b) => !dockDirection[b.dock][1]).reverse(),
      ...before,
      ...before2,
      ...after,
      ...after2,
      ...strips.filter((b) => dockDirection[b.dock][1])
    ];
  }
  changeStrips(strips, oldStrips) {
    const me = this, manager = me.$strips || (me.$strips = new DynamicObject({
      configName: "strips",
      factory: Widget,
      inferType: false,
      // the name of a bar in the strips object is not its type
      owner: me,
      created(instance) {
        var _a5;
        const { dock } = instance;
        if (!headerDock[dock] && !dockDirection[dock]) {
          throw new Error(
            `Invalid dock value "${dock}"; must be: top, left, right, bottom, header, or pre-header`
          );
        }
        FunctionHelper.after(instance, "onConfigChange", (ret, { name }) => {
          if (barConfigs[name]) {
            me.onConfigChange({
              name: "strips",
              value: manager.target
            });
          }
        });
        instance.innerItem = false;
        me.onChildAdd(instance);
        instance.parent = me;
        (_a5 = instance.layout) == null ? void 0 : _a5.renderChildren();
        if (instance.hasItems) {
          me.hasItems = true;
        }
      },
      setup(config, name) {
        config = ObjectHelper.merge(ObjectHelper.clone(me.stripDefaults["*"]), me.stripDefaults[name], config);
        config.parent = me;
        config.ref = name;
        return config;
      },
      transform(config) {
        if (Array.isArray(config)) {
          config = {
            items: config
          };
        }
        return config || null;
      }
    }));
    manager.update(strips);
    if (!oldStrips) {
      return manager.target;
    }
  }
  //endregion
  //region Collapse/Expand
  /**
   * This property is `true` if the panel is currently collapsing.
   * @property {Boolean}
   * @readonly
   * @category Layout
   */
  get collapsing() {
    var _a5;
    return (_a5 = this.collapsible) == null ? void 0 : _a5.collapsing;
  }
  /**
   * This property is `true` if the panel is currently either collapsing or expanding.
   * @property {Boolean}
   * @readonly
   * @internal
   * @category Layout
   */
  get collapsingExpanding() {
    var _a5;
    return (_a5 = this.collapsible) == null ? void 0 : _a5.collapsingExpanding;
  }
  /**
   * This property is `true` if the panel is currently expanding.
   * @property {Boolean}
   * @readonly
   * @category Layout
   */
  get expanding() {
    var _a5;
    return (_a5 = this.collapsible) == null ? void 0 : _a5.expanding;
  }
  changeCollapsed(value2) {
    const me = this, { collapsible } = me;
    me.recompose.flush();
    value2 = Boolean(value2);
    if (!collapsible || me.changingCollapse || !me.isPainted) {
      return value2;
    }
    collapsible == null ? void 0 : collapsible.collapse({
      animation: null,
      collapsed: value2
    });
  }
  changeCollapsible(collapsible, was) {
    const me = this;
    me.getConfig("tools");
    if (collapsible) {
      if (collapsible === true) {
        collapsible = {};
      } else if (typeof collapsible === "string") {
        collapsible = {
          [dockDirection[collapsible] ? "direction" : "type"]: collapsible
        };
      }
    }
    return PanelCollapser.reconfigure(was, collapsible, {
      owner: me,
      defaults: {
        panel: me
      },
      cleanup() {
        if (me.collapsed && !collapsible) {
          was.collapse({
            animation: null,
            collapsed: false
          });
          me._collapsed = 1;
        }
      }
    });
  }
  updateCollapsible(collapsible) {
    const me = this, tools = collapsible == null ? void 0 : collapsible.toolsConfig;
    me.tools = {
      collapse: (tools == null ? void 0 : tools.collapse) || null,
      recollapse: (tools == null ? void 0 : tools.recollapse) || null
    };
    if (collapsible && me.isPainted && me.collapsed) {
      me._collapsed = 1;
    }
    if (me.collapsed === 1) {
      me.collapsed = true;
    }
  }
  _collapse(collapsed, options) {
    var _a5;
    if (options !== true && options !== void 0) {
      if (options === false) {
        collapsed.collapsed = !collapsed.collapsed;
      } else if (typeof options === "number") {
        collapsed.animation = {
          duration: options
        };
      } else if (options === null) {
        collapsed.animation = options;
      } else if (options === true) {
      } else if ("animation" in options) {
        ObjectHelper.merge(collapsed, options);
      } else {
        collapsed.animation = options;
      }
    }
    return (_a5 = this.collapsible) == null ? void 0 : _a5.collapse(collapsed);
  }
  collapse(options) {
    return this.collapsePanel(options);
  }
  expand(options) {
    return this.expandPanel(options);
  }
  /**
   * Collapse the panel.
   * @async
   */
  collapsePanel(options) {
    return this._collapse({ collapsed: true }, options);
  }
  /**
   * Expand the panel.
   * @async
   */
  expandPanel(options) {
    return this._collapse({ collapsed: false }, options);
  }
  /**
   * Toggles collapsed state of the panel.
   * @param {Boolean} [collapse] Pass `true` to collapse or `false` to expand the panel, or omit to toggle.
   * @privateparam [options] options object with animation config (for internal use)
   * @returns {Boolean} `true` value for collapsed panel or `false` for expanded
   * @category Misc
   */
  toggleCollapsed(collapse, options) {
    if (typeof collapse === "boolean") {
      return collapse ? this.collapse(options) : this.expand(options);
    }
    return this.collapsed ? this.expand(options) : this.collapse(options);
  }
  //endregion
  //region Misc
  get expandedHeaderDock() {
    var _a5, _b2, _c2;
    return (_c2 = (_b2 = this._expandedHeaderDock) != null ? _b2 : (_a5 = this.initialConfig.header) == null ? void 0 : _a5.dock) != null ? _c2 : "top";
  }
  set expandedHeaderDock(v) {
    this._expandedHeaderDock = v == null ? void 0 : v.toLowerCase();
  }
  updateHeader(header) {
    if (!this.changingCollapse) {
      this.expandedHeaderDock = header == null ? void 0 : header.dock;
    }
  }
  updateTrapFocus(trapFocus) {
    var _a5;
    const me = this, { element } = me, { classList } = element, before = trapFocus === "before", after = trapFocus === "after";
    classList.toggle("b-focus-trapped", trapFocus && !before && !after);
    classList.toggle("b-focus-trapped-before", before);
    classList.toggle("b-focus-trapped-after", after);
    me.focusTrapListener = (_a5 = me.focusTrapListener) == null ? void 0 : _a5.call(me);
    me[trapFocus ? "addKeyBinding" : "removeKeyBinding"]("shift+Tab", "trapTabOut");
    if (trapFocus) {
      me.focusTrapListener = EventHelper.on({
        element,
        focusin: "onFocusTrapped",
        delegate: `.b-focus-trap`,
        thisObj: me
      });
      if (!me.treeWalker) {
        me.treeWalker = me.setupTreeWalker(element, DomHelper.NodeFilter.SHOW_ELEMENT, isTabbable);
      }
    }
  }
  setupTreeWalker(root, whatToShow, filter2) {
    return document.createTreeWalker(root, whatToShow, filter2);
  }
  trapTabOut(event) {
    if (event.target === this.element) {
      event.preventDefault();
      this.bottomFocusTrap.focus();
    } else {
      return false;
    }
  }
  onFocusTrapped(event) {
    const me = this, { target } = event, treeWalker = me.treeWalker, bottom = target === me.bottomFocusTrap, top = target === me.topFocusTrap;
    if (me.trigger("focusTrap", { target, bottom, top }) !== false) {
      if (top || bottom) {
        if (bottom || event.relatedTarget === me.element && event.shiftKey) {
          treeWalker.currentNode = me.topFocusTrap;
          treeWalker.nextNode();
        } else {
          treeWalker.currentNode = me.bottomFocusTrap;
          treeWalker.previousNode();
        }
        me.requestAnimationFrame(() => treeWalker.currentNode.focus());
      }
    }
  }
  get focusElement() {
    return this.hasItems && (super.focusElement || this.element);
  }
  get contentElement() {
    return this.element && this.bodyElement;
  }
  getPositionableLocation(position) {
    let parentElement, insertBefore;
    if (position === "before") {
      insertBefore = this.headerElement || this.bodyWrapRootElement;
    } else if (position === "after") {
      insertBefore = this.bottomFocusTrap;
    } else {
      parentElement = this.contentElement;
    }
    return [parentElement, insertBefore];
  }
  get widgetClassList() {
    var _a5;
    const me = this, result = super.widgetClassList;
    if (me.hasHeader) {
      result.push("b-panel-has-header", `b-header-dock-${((_a5 = me.header) == null ? void 0 : _a5.dock) || "top"}`);
    }
    if (me.tbar) {
      result.push("b-panel-has-top-toolbar");
    }
    if (me.bbar) {
      result.push("b-panel-has-bottom-toolbar");
    }
    return result;
  }
};
//region Config
__publicField(_Panel, "$name", "Panel");
__publicField(_Panel, "type", "panel");
var Panel = _Panel;
Panel.initClass();
Panel._$name = "Panel";

// ../Core/lib/Core/helper/ElementResizeHelper.js
var handleMap = {
  topStart: "top-start",
  topEnd: "top-end",
  bottomEnd: "bottom-end",
  bottomStart: "bottom-start",
  top: "top",
  end: "end",
  bottom: "bottom",
  start: "start"
};
var ElementResizeHelper = class extends Base.mixin(Events_default) {
  construct(config) {
    super.construct(config);
    if (!this.element) {
      throw new Error("ElementResizeHelper: `element` is required.");
    }
  }
  updateElement(element) {
    var _a5;
    if (globalThis.getComputedStyle(element).position === "static") {
      console.warn("ElementResizeHelper: `element` must be positioned (absolute or relative) for resizing to work.");
    }
    (_a5 = this.pointerDownListeners) == null ? void 0 : _a5.call(this);
    this.pointerDownListeners = EventHelper.on({
      element,
      pointerdown: "onResizePointerDown",
      delegate: ".b-resize-grip",
      thisObj: this
    });
  }
  updateHandles(handles) {
    const { element } = this;
    DomHelper.removeEachSelector(element, ".b-resize-grip");
    for (const handle in handles) {
      if (handles[handle] !== false) {
        const handleEl = document.createElement("div");
        handleEl.className = `b-resize-grip b-resize-grip-${handleMap[handle]}`;
        element.appendChild(handleEl);
      }
    }
  }
  onResizePointerDown(event) {
    var _a5;
    const me = this, { element } = me;
    me.handleType = event.target.className.replace("b-resize-grip b-resize-grip-", "").trim();
    if (event.button !== 0 || me.trigger("beforeResizeStart", { element, handle: me.handleType }) === false) {
      return;
    }
    event.preventDefault();
    const style = globalThis.getComputedStyle(element), rect = element.getBoundingClientRect(), topVal = parseFloat(style.top) || 0, startVal = parseFloat(style.insetInlineStart) || 0;
    me.initialX = event.clientX;
    me.initialY = event.clientY;
    me.initialTop = topVal;
    me.initialStart = startVal;
    me.initialWidth = rect.width;
    me.initialHeight = rect.height;
    me.trigger("resizeStart", { element, handle: me.handleType });
    (_a5 = me.removeMoveListeners) == null ? void 0 : _a5.call(me);
    me.removeMoveListeners = EventHelper.on({
      element: document.body,
      pointermove: "onResizePointerMove",
      thisObj: me
    });
    me.removeDocUpListeners = EventHelper.on({
      element: document,
      pointerup: "onResizePointerUp",
      capture: true,
      thisObj: me,
      once: true
    });
  }
  onResizePointerMove(event) {
    const me = this, {
      element,
      handleType,
      rtl,
      minHeight,
      minWidth,
      initialStart,
      initialTop,
      initialWidth,
      initialHeight,
      initialX,
      initialY,
      maxWidth,
      maxHeight
    } = me, dx = event.clientX - initialX, dy = event.clientY - initialY, minDx = initialWidth - (minWidth || 1), sign = rtl ? -1 : 1, { shiftKey } = event;
    let newStart = initialStart, newTop = initialTop, newWidth = initialWidth, newHeight = initialHeight;
    if (handleType.includes("start")) {
      newStart = initialStart + Math.min(dx, minDx) * sign;
      newWidth = initialWidth - dx * sign;
    } else if (handleType.includes("end")) {
      newWidth = initialWidth + dx * sign;
    }
    if (handleType.includes("top")) {
      newTop = initialTop + Math.min(dy, initialHeight - (minHeight || 1));
      newHeight = initialHeight - dy;
    } else if (handleType.includes("bottom")) {
      newHeight = initialHeight + dy;
    }
    if (shiftKey) {
      const aspectRatio = initialWidth / initialHeight;
      if (Math.abs(dx) > Math.abs(dy)) {
        newHeight = newWidth / aspectRatio;
      } else {
        newWidth = newHeight * aspectRatio;
      }
    }
    if (minWidth !== null) {
      newWidth = Math.max(newWidth, minWidth);
    }
    if (maxWidth !== null) {
      newWidth = Math.min(newWidth, maxWidth || document.body.offsetWidth);
    }
    if (minHeight !== null) {
      newHeight = Math.max(newHeight, minHeight);
    }
    if (maxHeight !== null) {
      newHeight = Math.min(newHeight, maxHeight || document.body.offsetWidth);
    }
    if (!rtl || !handleType.includes("start") || newWidth > minWidth) {
      element.style.insetInlineStart = Math.max(newStart, 0) + "px";
    }
    element.style.top = newTop + "px";
    element.style.width = newWidth + "px";
    element.style.height = newHeight + "px";
  }
  onResizePointerUp(event) {
    var _a5, _b2;
    (_a5 = this.removeDocUpListeners) == null ? void 0 : _a5.call(this);
    (_b2 = this.removeMoveListeners) == null ? void 0 : _b2.call(this);
    this.removeMoveListeners = null;
    this.removeDocUpListeners = null;
  }
  get isResizing() {
    return Boolean(this.removeMoveListeners);
  }
  doDestroy() {
    this.onResizePointerUp();
    this.pointerDownListeners();
    DomHelper.removeEachSelector(this.element, ".b-resize-grip");
    super.doDestroy();
  }
};
__publicField(ElementResizeHelper, "configurable", {
  element: null,
  // Minimum and maximum sizes
  minWidth: 50,
  maxWidth: null,
  minHeight: 50,
  maxHeight: null,
  rtl: null,
  /**
   * Configuration object defining which resize handles to display.
   * Set a property to `false` to hide the corresponding handle.
   * @prp {Object}
   * @property {Boolean} [topStart=true] Show/hide the top-start corner resize handle
   * @property {Boolean} [top=true] Show/hide the top edge resize handle
   * @property {Boolean} [topEnd=true] Show/hide the top-end corner resize handle
   * @property {Boolean} [end=true] Show/hide the end edge resize handle
   * @property {Boolean} [bottomEnd=true] Show/hide the bottom-end corner resize handle
   * @property {Boolean} [bottom=true] Show/hide the bottom edge resize handle
   * @property {Boolean} [bottomStart=true] Show/hide the bottom-start corner resize handle
   * @property {Boolean} [start=true] Show/hide the start edge resize handle
   */
  handles: {
    topStart: true,
    top: true,
    topEnd: true,
    end: true,
    bottomEnd: true,
    bottom: true,
    bottomStart: true,
    start: true
  }
});
ElementResizeHelper._$name = "ElementResizeHelper";

// ../Core/lib/Core/widget/mixin/Resizable.js
var Resizable_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    updateResizable(value2) {
      var _a6;
      const me = this;
      if (value2) {
        const config = typeof value2 === "object" ? value2 : {};
        me.resizeHelper = new ElementResizeHelper(ObjectHelper.assign({
          element: me.element,
          internalListeners: {
            resizeStart: me.onResizeStart,
            thisObj: me
          }
        }, config));
      } else {
        (_a6 = me.resizeHelper) == null ? void 0 : _a6.destroy();
      }
    }
    onResizeStart() {
      this.alignTo(null);
    }
    doDestroy() {
      var _a6;
      (_a6 = this.resizeHelper) == null ? void 0 : _a6.destroy();
      super.doDestroy();
    }
    updateRtl(rtl) {
      super.updateRtl(rtl);
      if (this.resizeHelper) {
        this.resizeHelper.rtl = rtl;
      }
    }
  }, __publicField(_a5, "$name", "Resizable"), __publicField(_a5, "configurable", {
    /**
     * Configure this property to allow the widget/component to be resized. Pressing <kbd>Shift</kbd> while resizing will
     * constrain the aspect ratio.
     *
     * - **`false`** (the default) disables resizing entirely.
     * - **`true`** enables resizing on all edges and corners.
     * - An **Object** allows fine-grained control, for example specifying which edges or corners have handles.
     *
     * Example usage:
     *
     * ```js
     * // Resizable with default handles (all edges/corners)
     * new SomePopup({
     *   resizable : true
     * });
     *
     * // Resizable with only start and bottom edges active
     * new SomePopup({
     *   resizable : {
     *     handles : {
     *       top         : false,
     *       end       : false,
     *       bottom      : true,
     *       start        : true,
     *       // corners also available: topStart, topEnd, bottomStart, bottomEnd
     *       // all omitted properties default to `true` if unspecified
     *     }
     *   }
     * });
     * ```
     *
     * @prp {Boolean|Object} resizable
     * @default false
     * @property {Number} [resizable.minWidth=50]   The minimum width of the element.
     * @property {Number} [resizable.maxWidth]      The maximum width of the element.
     * @property {Number} [resizable.minHeight=50]  The minimum height of the element.
     * @property {Number} [resizable.maxHeight]     The maximum height of the element
     * @property {Object} [resizable.handles]       An object controlling which edges/corners are resizable.
     * @property {Boolean} [resizable.handles.topStart=true]      Resizable from top-start corner
     * @property {Boolean} [resizable.handles.top=true]          Resizable from top edge
     * @property {Boolean} [resizable.handles.topEnd=true]     Resizable from top-end corner
     * @property {Boolean} [resizable.handles.end=true]        Resizable from end edge
     * @property {Boolean} [resizable.handles.bottomEnd=true]  Resizable from bottom-end corner
     * @property {Boolean} [resizable.handles.bottom=true]       Resizable from bottom edge
     * @property {Boolean} [resizable.handles.bottomStart=true]   Resizable from bottom-start corner
     * @property {Boolean} [resizable.handles.start=true]         Resizable from start edge
     */
    resizable: null
  }), _a5;
};

// ../Core/lib/Core/widget/Popup.js
var _Popup = class _Popup extends Panel.mixin(Resizable_default) {
  //endregion
  //region Init & destroy
  finalizeInit() {
    const me = this, { forElement } = me;
    me.anchoredTo = forElement;
    me.initialAnchor = me.anchor;
    if (forElement && me.showOnClick !== false) {
      if (!me.initialConfig.autoShow) {
        me.autoShow = false;
      }
      EventHelper.on({
        element: forElement,
        [me.showOnClick === 2 ? "contextmenu" : "click"]: "onElementUserAction",
        thisObj: me,
        capture: true
      });
    }
    super.finalizeInit();
    if (me.autoShow && (!forElement || DomHelper.isVisible(forElement))) {
      if (me.autoShow === true) {
        me.show();
      } else {
        me.setTimeout(() => me.show(), me.autoShow);
      }
    }
  }
  onInternalPaint({ firstPaint }) {
    var _a5;
    (_a5 = super.onInternalPaint) == null ? void 0 : _a5.call(this, ...arguments);
    const me = this;
    if (firstPaint && me.headerElement) {
      EventHelper.on({
        element: me.headerElement,
        dblclick: me.onHeaderDblClick,
        thisObj: me
      });
    }
  }
  doDestroy() {
    this.syncModalMask();
    super.doDestroy();
  }
  //endregion
  compose() {
    const { hasNoChildren, textContent } = this;
    return {
      class: {
        // Popup has extra CSS responsibilities at the top level.
        // The CSS needs to know whether it should impose a max-width.
        "b-text-popup": Boolean(textContent && hasNoChildren)
      }
    };
  }
  //region Show/hide
  /**
   * Performs the configured {@link #config-closeAction} upon this popup.
   * By default, the popup hides. The {@link #config-closeAction} may be
   * configured as `'destroy'`.
   * @fires beforeClose
   * @async
   */
  close() {
    if (!this._hidden) {
      const result = this.trigger("beforeClose");
      if (ObjectHelper.isPromise(result)) {
        result.then((result2) => {
          if (result2 !== false) {
            this.doClose();
          }
        });
        return result;
      }
      if (result === false) {
        return result;
      }
    }
    this.doClose();
  }
  doClose() {
    var _a5, _b2;
    const me = this, { closeAction } = me;
    if (!me._hidden || closeAction === "destroy") {
      me.modal && closeAction === "destroy" && me.revertFocus();
      me.unmask();
      const errorTip = me.items.length && ((_a5 = me.items.find((item) => item.errorTip)) == null ? void 0 : _a5.errorTip);
      if (errorTip) {
        (_b2 = errorTip.pointerOverOutDetacher) == null ? void 0 : _b2.call(errorTip);
        errorTip.hide();
      }
      return me[closeAction]();
    }
  }
  toggleMaximized() {
    this.maximized = !this.maximized;
  }
  updateMaximized(value2) {
    value2 = Boolean(value2);
    this.element.classList.toggle("b-maximized", Boolean(value2));
    this.draggable && (this.draggable.disabled = value2);
  }
  //endregion
  //region Events
  onInternalKeyDown(event) {
    const me = this;
    if (event.key === "Escape" && me.closeOnEscape) {
      event.stopImmediatePropagation();
      if (me.floating || me.positioned) {
        me.close(true);
      } else if (me.collapsible) {
        if (me.revealed) {
          me.collapsible.toggleReveal();
        } else {
          me.collapse();
        }
      }
    }
  }
  onDocumentMouseDown({ event }) {
    const me = this, { owner } = me, { target } = event;
    if (!me.isVisible) {
      return;
    }
    if (event.type !== "touchend" && (owner == null ? void 0 : owner.isButton) && owner._menu === me && owner.element.contains(target)) {
      event.preventDefault();
      return false;
    }
    if (me.modal && target === _Popup.modalMask) {
      event.preventDefault();
      if (me.modal.closeOnMaskTap) {
        me.close();
      } else if (!me.containsFocus) {
        me.focus();
      }
    } else if (!me.owns(target, true) && me.autoClose && event.type === "mousedown") {
      if (me.containsFocus) {
        const focusedEl = DomHelper.getActiveElement(me.element);
        EventHelper.on({
          element: document,
          once: true,
          expires: 100,
          [event.type]: (e) => {
            if (e.defaultPrevented && DomHelper.getActiveElement(me.element) === focusedEl) {
              me.close();
            }
          }
        });
      } else {
        me.close();
      }
    }
  }
  get isTopModal() {
    return !this._isRevertingFocus && DomHelper.isVisible(_Popup.modalMask) && this.element.previousElementSibling === _Popup.modalMask;
  }
  onFocusIn(e) {
    const activeEl = DomHelper.getActiveElement(this);
    super.onFocusIn(e);
    if (DomHelper.getActiveElement(this) === activeEl && e.target === this.element) {
      this.focus();
    }
  }
  onFocusOut(e) {
    const usingVirtualKeyboard = globalThis.visualViewport && globalThis.visualViewport.height < document.documentElement.clientHeight - 1;
    if (!usingVirtualKeyboard && !this.modal && this.autoClose) {
      this.close();
    }
    super.onFocusOut(e);
  }
  afterShow() {
    const me = this;
    if (me.autoClose || me.modal) {
      me.addDocumentMouseDownListener();
    }
    super.afterShow(...arguments);
    me.toFront();
  }
  toFront() {
    if (this.floating) {
      super.toFront();
      this.syncModalMask();
    }
    if (this.focusOnToFront && !this.containsFocus) {
      this.focus();
    }
  }
  addDocumentMouseDownListener() {
    if (!this.mouseDownRemover) {
      this.mouseDownRemover = GlobalEvents_default.ion({
        globaltap: "onDocumentMouseDown",
        thisObj: this
      });
    }
  }
  updateModal(modal) {
    const me = this;
    if (modal) {
      me.element.setAttribute("aria-modal", true);
      me.floating = true;
      if (typeof me._x !== "number" && typeof me._y !== "number" && !me.hasConfig("centered")) {
        me.centered = true;
        me.anchor = false;
      }
    } else {
      me.element.removeAttribute("aria-modal");
    }
  }
  syncModalMask() {
    const me = this, {
      modal,
      element
    } = me;
    if (modal && me.isVisible) {
      me.modalMask.classList.remove("b-hide-display");
      me.modalMask.classList.toggle("b-modal-transparent", Boolean(modal.transparent));
      element.classList.add("b-modal");
      me.modalMask.setAttribute("owned-by", me.id);
    } else if (me.isPainted) {
      element.classList.remove("b-modal");
      const remainingModals = me.floatRoot.querySelectorAll(".b-modal"), topModal = remainingModals.length ? _Popup.fromElement(remainingModals[remainingModals.length - 1], "popup") : null;
      if (topModal) {
        topModal.syncModalMask();
      } else {
        _Popup.modalMask.classList.add("b-hide-display");
      }
    }
  }
  afterHide() {
    var _a5;
    const me = this;
    super.afterHide(...arguments);
    (_a5 = me.mouseDownRemover) == null ? void 0 : _a5.call(me);
    me.mouseDownRemover = null;
    me.syncModalMask();
  }
  onElementUserAction(event) {
    const { showOnClick } = this;
    if (typeof showOnClick !== "number" || showOnClick === event.button) {
      if (event.button === 2) {
        event.preventDefault();
      }
      this.show();
    }
  }
  onHeaderDblClick() {
    if (this.maximizable) {
      this.toggleMaximized();
    }
  }
  //endregion
  updateClosable(closable) {
    this.tools.close.hidden = !closable;
  }
  updateMaximizable(maximizable) {
    this.tools.maximize.hidden = !maximizable;
  }
  /**
   * Returns the modal mask element for this Popup correctly positioned just below this Popup.
   * @internal
   */
  get modalMask() {
    const { modalMask } = _Popup;
    if (modalMask.nextElementSibling !== this.element) {
      this.floatRoot.insertBefore(modalMask, this.element);
    }
    return modalMask;
  }
  /**
   * Returns the modal mask element. It does NOT guarantee its placement in the DOM relative
   * to any Popup. To get the modal mask for a particular Popup, use the instance property.
   * @internal
   */
  static get modalMask() {
    if (!this._modalMask) {
      this._modalMask = DomHelper.createElement({
        className: "b-modal-mask b-hide-display"
      });
      EventHelper.on({
        element: this._modalMask,
        wheel: (e) => e.preventDefault()
      });
    }
    return this._modalMask;
  }
};
//region Config
__publicField(_Popup, "$name", "Popup");
__publicField(_Popup, "type", "popup");
__publicField(_Popup, "configurable", {
  /**
   * Auto show flag for Popup.
   * If truthy then Popup is shown automatically upon hover.
   * @config {Boolean}
   * @default
   */
  autoShow: true,
  /**
   * By default, a Popup is transient, and will {@link #function-close} when the user clicks or
   * taps outside its owned widgets and when focus moves outside its owned widgets.
   *
   * **Note**: {@link #config-modal Modal} popups won't {@link #function-close} when focus moves outside even
   * if autoClose is `true`.
   *
   * Configure as `false` to make a Popup non-transient.
   * @config {Boolean}
   * @default
   */
  autoClose: true,
  /**
   * Show popup when user clicks the element that it is anchored to. Cannot be combined with showOnHover. Can
   * also be provided as the button number (0: main button, 2: right button).
   * @config {Boolean|Number}
   * @default
   */
  showOnClick: false,
  /**
   * DOM element to attach popup.
   * @config {HTMLElement}
   */
  forElement: null,
  monitorResize: true,
  floating: true,
  hidden: true,
  axisLock: true,
  // Flip edges if align violates constrainTo
  /**
   * Set to `false` to prevent dragging the popup element.
   *
   * @config {Boolean|Object}
   * @property {String} handleSelector CSS selector used to determine if a drag operation can be started from
   * a mouse-downed element inside the popup
   * @default true
   * @category Float & align
   */
  draggable: true,
  // blacklist buttons and field inners, and other draggable elements
  draggableBlacklistSelector: "button,.b-field-inner,.b-gridbase,.b-calendarmixin,.b-resize-grip,input",
  hideAnimation: {
    opacity: {
      from: 1,
      to: 0,
      duration: ".3s",
      delay: "0s"
    }
  },
  showAnimation: {
    opacity: {
      from: 0,
      to: 1,
      duration: ".4s",
      delay: "0s"
    }
  },
  stripDefaults: {
    bbar: {
      layout: {
        justify: "flex-end"
      }
    }
  },
  testConfig: {
    hideAnimation: null,
    showAnimation: null
  },
  /**
   * The action to take when calling the {@link #function-close} method.
   * By default, the popup is hidden.
   *
   * This may be set to `'destroy'` to destroy the popup upon close.
   * @config {'hide'|'destroy'}
   * @default
   */
  closeAction: "hide",
  /**
   * By default, tabbing within a Popup is circular - that is it does not exit.
   * Configure this as `false` to allow tabbing out of the Popup.
   * @config {Boolean}
   * @default
   */
  trapFocus: true,
  /**
   * By default a Popup is focused when it is shown.
   * Configure this as `false` to prevent automatic focus on show.
   * @config {Boolean}
   * @default
   */
  focusOnToFront: true,
  /**
   * Show a tool in the header to close this Popup.
   * The tool is available in the {@link Core.widget.mixin.Toolable#property-tools} object
   * under the name `close`. It uses the CSS class `b-popup-close` to apply a
   * default close icon. This may be customized with your own CSS rules.
   * @default false
   * @config {Boolean}
   */
  closable: null,
  /**
   * Show a tool in the header to maximize this popup
   * @config {Boolean}
   * @default false
   */
  maximizable: null,
  /**
   * Optionally show an opaque mask below this Popup when shown.
   * Configure this as `true` to show the mask.
   *
   * When a Popup is modal, it defaults to being {@link Core.widget.Widget#config-centered}.
   * Also, it won't {@link #function-close} when focus moves outside even if {@link #config-autoClose} is
   * `true`.
   *
   * The default action is to focus the popup.
   *
   * Usage:
   * ```javascript
   * new Popup({
   *     title  : 'I am modal',
   *     modal  : {
   *         closeOnMaskTap : true
   *     },
   *     height : 100,
   *     width  : 200
   * });
   * ```
   *
   * @default false
   * @config {Boolean|Object}
   * @property {Boolean} [closeOnMaskTap=false] Specify as `true` to close when mask is tapped.
   * @property {Boolean} [transparent=false] Specify as `true` to use a transparent mask.
   */
  modal: null,
  /**
   * Set to `true` to make this widget take all available space in the visible viewport.
   * @member {Boolean} maximized
   * @category Float & align
   */
  /**
   * Set to `true` to make this widget take all available space in the visible viewport.
   * @config {Boolean}
   * @default false
   * @category Float & align
   */
  maximized: null,
  tools: {
    close: {
      cls: "b-popup-close",
      handler: "close",
      weight: -1e3,
      ariaLabel: "L{Popup.close}",
      hidden: true
      // shown when closable set to `true`
    },
    maximize: {
      cls: "b-popup-expand",
      handler: "toggleMaximized",
      weight: -999,
      ariaLabel: "L{Popup.maximize}",
      hidden: true
      // shown when maximizable set to `true`
    }
  },
  highlightReturnedFocus: true,
  role: "dialog",
  /**
   * Close popup when `ESC` key is pressed.
   * @default true
   * @config {Boolean}
   */
  closeOnEscape: true
});
var Popup = _Popup;
Popup.initClass();
Popup._$name = "Popup";

// ../Core/lib/Core/widget/MenuItem.js
var bIcon2 = /^b-icon-/;
var bFa2 = /^b-fa-/;
var MenuItem = class extends Widget {
  static get configurable() {
    return {
      /**
       * If configured with a `Boolean` value, a checkbox is displayed
       * as the start icon, and the {@link #event-toggle} event is fired
       * when the checked state changes.
       * `String` value allows to link value by reference name.
       *
       * Example:
       * ```javascript
       * menu : {
       *     items : {
       *         fill : {
       *             text    : 'Fill width',
       *             checked : 'up.resourceColumns.fillWidth'
       *         },
       *         fit : {
       *             text        : 'Fit width',
       *             checked     : false,
       *             closeParent : true
       *         }
       *     }
       * }
       * ```
       *
       * @config {Boolean|String}
       */
      checked: null,
      /**
       * Set to `true` to display a border above this menu item, if there are other visible menu items before it.
       * @config {Boolean}
       * @default false
       */
      separator: null,
      /**
       * Indicates that this menu item is part of a group where only one can be checked. Assigning a value
       * also sets `toggleable` to `true`.
       * ```
       * const yesButton = new Button({
       *    toggleGroup : 'yesno',
       *    text        : 'Yes'
       * });
       *
       * const noButton = new Button({
       *    toggleGroup : 'yesno',
       *    text        : 'No'
       * });
       * ```
       * @prp {String}
       */
      toggleGroup: null,
      /**
       * Returns the instantiated menu widget as configured by {@link #config-menu}.
       * @member {Core.widget.Widget} menu
       * @readonly
       */
      /**
       * A submenu configuration object, or an array of MenuItem configuration
       * objects from which to create a submenu.
       *
       * Configuration object example:
       * ```javascript
       * new Menu({
       *     // Menu items
       *     items : {
       *         move : {
       *             text : 'Main item',
       *             menu : {
       *                 // Submenu items
       *                 firstItem : {
       *                     text : 'Sub-item 1',
       *                     onItem({ eventRecord }) {}
       *                 },
       *                 secondItem : {
       *                     text : 'Sub-item 2',
       *                     onItem({ eventRecord }) {}
       *                 }
       *             }
       *         }
       *     }
       * });
       * ```
       *
       * Array of items example:
       * ```javascript
       * new Menu({
       *     // Menu items
       *     items : {
       *         move : {
       *             text : 'Main item',
       *             // Submenu items
       *             menu : [
       *                 {
       *                     text : 'Sub-item 1',
       *                     onItem({ eventRecord }) {}
       *                 },
       *                 {
       *                     text : 'Sub-item 2',
       *                     onItem({ eventRecord }) {}
       *                 }
       *             ]
       *         }
       *     }
       * });
       * ```
       *
       * Note that this does not have to be a Menu. The `type` config can be used to specify any widget as the submenu.
       * ```javascript
       * new Menu({
       *     // Menu items
       *     items : {
       *         move : {
       *             text : 'Main item',
       *             // Submenu items
       *             menu : [
       *                 {
       *                     type  : 'textfield',
       *                     label : 'Type here'
       *                 },
       *                 {
       *                     type : 'button',
       *                     text : 'Confirm'
       *                 }
       *             ]
       *         }
       *     }
       * });
       * ```
       *
       * @config {Object<String,MenuItemConfig|ContainerItemConfig>|Array<MenuItemConfig|ContainerItemConfig>}
       */
      menu: {
        value: null,
        $config: ["lazy", "nullify"]
      },
      menuDefaults: {
        type: "menu",
        align: "s0-e0",
        anchor: true,
        autoClose: true,
        floating: true,
        autoShow: false,
        cls: {
          "b-sub-menu": 1
          // Marks the widget as a submenu
        }
      },
      /**
       * Item icon class.
       *
       * All [Font Awesome](https://fontawesome.com/cheatsheet) icons may also be specified as `'b-fa-' + iconName`.
       *
       * Otherwise this is a developer-defined CSS class string which results in the desired icon.
       * @config {String}
       */
      icon: null,
      /**
       * The text to be displayed in the item. The text is automatically HTML encoded.
       * @prp {String|DomConfig|DomConfig[]} text
       */
      text: null,
      /**
       * By default, upon activate, non-checkbox menu items will collapse
       * the owning menu hierarchy.
       *
       * Configure this as `false` to cause the menu to persist after
       * activating an item
       * @config {Boolean}
       */
      closeParent: null,
      /**
       * If provided, turns the menu item into a link
       * @config {String}
       */
      href: null,
      radio: null,
      /**
       * The `target` attribute for the {@link #config-href} config
       * @config {'_self'|'_blank'|'_parent'|'_top'|null}
       */
      target: null,
      localizableProperties: ["text"],
      role: "menuitem",
      openMenuDelay: 120
    };
  }
  updateElement(element, oldElement) {
    const me = this, result = super.updateElement(element, oldElement);
    if (typeof me.checked === "boolean") {
      me.role = "menuitemcheckbox";
    } else if (me.href) {
      me.role = null;
    }
    me.ariaHasPopup = me.hasMenu ? "menu" : false;
    return result;
  }
  compose() {
    var _a5;
    const me = this, { checked, href, hasMenu, radio, separator, target, text } = me, isCheckItem = typeof checked === "boolean", icon = me.icon || (isCheckItem ? "b-fw-icon" : ""), checkCls = `b-icon-${radio ? "radio-" : ""}`, hasCustomContent = typeof text === "object";
    return {
      tag: href ? "a" : "div",
      tabIndex: -1,
      href,
      target,
      class: {
        "b-has-submenu": hasMenu,
        "b-checked": checked,
        // Support both separator config and directly setting separator class
        "b-separator": separator || ((_a5 = me.cls) == null ? void 0 : _a5["b-separator"])
      },
      dataset: {
        group: me.toggleGroup
      },
      // Only set aria-checked if it's a check item
      [isCheckItem ? "aria-checked" : ""]: checked,
      // Only set expanded if there's a submenu to expand
      [hasMenu ? "aria-expanded" : ""]: false,
      children: {
        iconElement: icon && {
          // This element is a purely visual cue with no meaning to the A11Y tree
          "aria-hidden": true,
          tag: "i",
          class: {
            "b-fa": bFa2.test(icon),
            "b-icon": bIcon2.test(icon),
            "b-menuitem-icon": 1,
            [`${checkCls}checked`]: checked === true,
            [`${checkCls}unchecked`]: checked === false,
            ...DomClassList.normalize(icon, "object")
          }
        },
        textElement: {
          tag: "span",
          [hasCustomContent ? void 0 : "text"]: hasCustomContent ? void 0 : text,
          class: {
            "b-menu-text": 1,
            "b-menu-custom-content": hasCustomContent
          },
          children: hasCustomContent ? ArrayHelper.asArray(text) : null
        },
        subMenuIcon: hasMenu && {
          // This element is a purely visual cue with no meaning to the A11Y tree
          "aria-hidden": true,
          tag: "i",
          class: {
            "b-fw-icon": 1,
            "b-icon-sub-menu": 1
          }
        }
      }
    };
  }
  /**
   * Actions this item. Fires the {@link #event-item} event, and if this is a {@link #config-checked} item, toggles
   * the checked state, firing the {@link #event-toggle} event.
   */
  doAction(event) {
    const item = this, menu = this.parent, itemEvent = { menu, item, element: item.element, bubbles: true, domEvent: event };
    if (typeof item.checked === "boolean") {
      const newCheckedState = !item.checked;
      if (!item.toggleGroup || newCheckedState) {
        item.checked = !item.checked;
      }
    } else if (item.href && event.key) {
      item.element.click();
    }
    item.trigger("beforeItem", itemEvent);
    item.trigger("item", itemEvent);
    if (item.closeParent && menu) {
      menu.rootMenu.close();
      if (event && !item.href) {
        event.preventDefault();
      }
    }
  }
  get focusElement() {
    return this.element;
  }
  get contentElement() {
    return this.textElement;
  }
  get isFocusable() {
    const { focusElement } = this;
    return focusElement && this.isVisible && (focusElement === document.body || focusElement.offsetParent);
  }
  get hasMenu() {
    return this.hasConfig("menu");
  }
  get childItems() {
    const { _menu } = this;
    return _menu ? [_menu] : [];
  }
  get radio() {
    var _a5;
    return Boolean((_a5 = this._radio) != null ? _a5 : this.toggleGroup);
  }
  onFocusIn(e) {
    const { menu } = this;
    super.onFocusIn(e);
    if (!this.disabled && menu) {
      menu.clearTimeout(menu.closeTimer);
      this.delay("openMenu", menu.closeTimer ? 0 : this.openMenuDelay);
      menu.closeTimer = null;
    }
  }
  onFocusOut(e) {
    var _a5;
    const me = this;
    me.clearTimeout("openMenu");
    super.onFocusOut(e);
    const menu = me._menu;
    if (menu && !((_a5 = me.owner) == null ? void 0 : _a5.ignoreFocusOut)) {
      if (DomHelper.usingKeyboard) {
        me.closeMenu();
      } else {
        menu.clearTimeout(menu.closeTimer);
        menu.closeTimer = menu.setTimeout(() => me.closeMenu(), menu.closeMenuDelay);
        menu.once("hide", () => menu.clearTimeout(menu.closeTimer));
      }
    }
  }
  openMenu(andFocus) {
    const { menu, parent } = this;
    if (!this.disabled && menu) {
      menu.focusOnToFront = andFocus;
      parent.isMenu ? parent.openSubMenu(this.element, this) : menu.show();
    }
  }
  onChildShow(shown) {
    super.onChildShow(shown);
    this.ariaElement.setAttribute("aria-expanded", true);
  }
  closeMenu() {
    if (this._menu.isWidget) {
      this.menu.close();
    }
    this.menu.closeTimer = null;
  }
  onChildHide(hidden) {
    super.onChildHide(hidden);
    this.ariaElement.setAttribute("aria-expanded", false);
  }
  changeToggleGroup(toggleGroup) {
    if (toggleGroup && typeof this.checked !== "boolean") {
      this.checked = false;
    }
    return toggleGroup;
  }
  /**
   * Get/sets the checked state of this `MenuItem` and fires the {@link #event-toggle}
   * event upon change.
   *
   * Note that this must be configured as a `Boolean` to enable the checkbox UI.
   * @member {Boolean} checked
   */
  changeChecked(checked, old) {
    if (this.isConfiguring || typeof old === "boolean") {
      return Boolean(checked);
    }
  }
  updateChecked(checked) {
    const me = this;
    if (!me.isConfiguring) {
      if (me.toggleGroup) {
        me.uncheckToggleGroupMembers();
      }
      me.element.setAttribute("aria-checked", checked);
      me.trigger("toggle", {
        menu: me.owner,
        item: me,
        element: me.element,
        bubbles: true,
        checked
      });
    }
  }
  getToggleGroupMembers() {
    const me = this, { checked, toggleGroup, element } = me, result = [];
    if (checked && toggleGroup) {
      DomHelper.forEachSelector(me.rootElement, `[data-group=${toggleGroup}]`, (otherElement) => {
        if (otherElement !== element) {
          const partnerCheckItem = Widget.fromElement(otherElement);
          partnerCheckItem && result.push(partnerCheckItem);
        }
      });
    }
    return result;
  }
  uncheckToggleGroupMembers() {
    if (this.checked && this.toggleGroup) {
      this.getToggleGroupMembers().forEach((widget) => widget.checked = false);
    }
  }
  get closeParent() {
    const result = typeof this.checked === "boolean" ? this._closeParent : this._closeParent !== false;
    return result && !this.hasMenu;
  }
  changeMenu(config, existingMenu) {
    const me = this, { constrainTo, scrollAction, focusOnHover } = me.owner;
    if (config && typeof config === "object" && (!("type" in config) || config.type === "menu") && !("items" in config) && !("widgets" in config) && !("html" in config)) {
      config = {
        lazyItems: config
      };
    }
    return Menu.reconfigure(existingMenu, config ? Menu.mergeConfigs(me.menuDefaults, {
      owner: me,
      forElement: me.element,
      ariaLabel: me.text,
      [
        // New Menus are floating, not positioned
        existingMenu ? "positioned" : "_"
      ]: false,
      focusOnHover,
      constrainTo,
      scrollAction
    }, config) : null, me);
  }
};
//region Config
__publicField(MenuItem, "$name", "MenuItem");
__publicField(MenuItem, "type", "menuitem");
MenuItem.initClass();
MenuItem._$name = "MenuItem";

// ../Core/lib/Core/widget/Menu.js
var validKeys = {
  ArrowUp: 1,
  ArrowDown: 1,
  ArrowRight: 1,
  ArrowLeft: 1,
  Enter: 1,
  Escape: 1
};
var Menu = class extends Popup {
  /**
   * Currently open sub menu, if any
   * @member {Core.widget.Menu} currentSubMenu
   * @readonly
   */
  //endregion
  /**
   * A descendant menu item has been activated.
   *
   * Note that this event bubbles up through parents and can be
   * listened for on a top level {@link Core.widget.Menu} for convenience.
   * @event item
   * @param {Core.widget.MenuItem} item - The menu item which is being actioned.
   * @param {Core.widget.Menu} menu - Menu containing the menu item
   */
  /**
   * The checked state of a descendant menu item has changed.
   *
   * Note that this event bubbles up through parents and can be
   * listened for on a top level {@link Core.widget.Menu} for convenience.
   * @event toggle
   * @param {Core.widget.MenuItem} item - The menu item whose checked state changed.
   * @param {Core.widget.Menu} menu - Menu containing the menu item
   * @param {Boolean} checked - The _new_ checked state.
   */
  /* break doc comment */
  //region Init
  construct(config) {
    if (Array.isArray(config)) {
      config = {
        lazyItems: config
      };
    }
    super.construct(config);
    EventHelper.on({
      element: this.element,
      click: "onMouseClick",
      mouseover: "onMouseOver",
      mouseenter: "onMouseEnter",
      mouseleave: "onMouseLeave",
      thisObj: this
    });
  }
  afterShow() {
    const { items: items2, element } = this, hasSubmenu = items2.some((item) => item.hasConfig("menu"));
    if (hasSubmenu) {
      element.classList.add("b-menu-with-submenu");
    }
    const hasIcon = items2.some((item) => item.icon);
    if (hasIcon) {
      element.classList.add("b-menu-with-icon");
    }
    super.afterShow(...arguments);
  }
  createWidget(item) {
    if (typeof item === "string") {
      item = {
        text: item
      };
    }
    return super.createWidget(item);
  }
  get focusElement() {
    var _a5;
    const me = this, fromParentMenu = (_a5 = me.parentMenu) == null ? void 0 : _a5.element.contains(DomHelper.getActiveElement(me.parentMenu)), firstWidget = me.items[0];
    if (fromParentMenu || DomHelper.usingKeyboard || !(firstWidget instanceof MenuItem)) {
      return super.focusElement;
    }
    return me.element;
  }
  //endregion
  onDocumentMouseDown({ event }) {
    if (!this.parentMenu || !this.parentMenu.owns(event.target)) {
      return super.onDocumentMouseDown(...arguments);
    }
  }
  //region Show
  hide(animate) {
    var _a5, _b2;
    const me = this;
    const result = super.hide(animate);
    if (!me.isVisible) {
      (_b2 = (_a5 = me.currentSubMenu) == null ? void 0 : _a5.hide) == null ? void 0 : _b2.call(_a5, animate);
      if (me.parentMenu) {
        me.parentMenu.currentSubMenu = null;
      }
    }
    return result;
  }
  show() {
    const result = super.show(...arguments);
    const { parentMenu } = this;
    if (this.isVisible && parentMenu) {
      parentMenu.currentSubMenu = this;
    }
    return result;
  }
  //endregion
  //region Events
  /**
   * Activates a menu item if user clicks on it
   * @private
   */
  onMouseClick(event) {
    const menuItem = event.target.closest(".b-menuitem");
    if (menuItem) {
      this.triggerElement(menuItem, event);
    }
  }
  /**
   * Activates menu items on hover. On real mouse hover, not on a touchstart.
   * @private
   */
  onMouseOver(event) {
    var _a5;
    if (this.focusOnHover !== false) {
      const fromItemElement = (_a5 = event.relatedTarget) == null ? void 0 : _a5.closest(".b-widget"), toItemElement = event.target.closest(".b-widget"), overItem = Widget.fromElement(toItemElement);
      if (!DomHelper.isTouchEvent && toItemElement && toItemElement !== fromItemElement && overItem.parent === this) {
        this.setTimeout({
          fn: "handleMouseOver",
          delay: 30,
          args: [overItem],
          cancelOutstanding: true
        });
      }
    }
  }
  handleMouseOver(overItem) {
    overItem.focus();
  }
  onMouseEnter() {
    this.clearTimeout(this.closeTimer);
    const { parentMenu } = this;
    if (parentMenu) {
      parentMenu.ignoreFocusOut = true;
      parentMenu.setTimeout(() => parentMenu.ignoreFocusOut = false, 500);
      parentMenu.clearTimeout(parentMenu.hideSubMenuTimer);
    }
  }
  // unselect any menu item if mouse leaves the menu element (unless it enters a child menu)
  onMouseLeave(event) {
    const me = this, { relatedTarget } = event, leavingToChild = relatedTarget && me.owns(relatedTarget);
    let targetCmp = relatedTarget instanceof HTMLElement && Widget.fromElement(relatedTarget), shouldHideMenu = !leavingToChild && me.focusOnHover !== false;
    if (targetCmp) {
      while (targetCmp.ownerCmp) {
        targetCmp = targetCmp.ownerCmp;
      }
      shouldHideMenu &= !DomHelper.getAncestor(targetCmp.element, [event.target]);
    }
    if (!leavingToChild && shouldHideMenu) {
      if (me.currentSubMenu) {
        me.hideSubMenuTimer = me.setTimeout(() => {
          var _a5;
          return (_a5 = me.currentSubMenu) == null ? void 0 : _a5.hide();
        }, me.closeMenuDelay);
      }
      if (me.element.contains(DomHelper.getActiveElement(me)) && DomHelper.getActiveElement(me).matches(".b-menuitem")) {
        me.focusElement.focus();
      }
    }
  }
  /**
   * Keyboard navigation. Up/down, close with esc, activate with enter
   * @private
   */
  onInternalKeyDown(event) {
    const sourceWidget = Widget.fromElement(event), isFromWidget = sourceWidget && sourceWidget !== this && !(sourceWidget instanceof MenuItem);
    if (event.key === "Escape") {
      (isFromWidget ? this : this.rootMenu).close();
      return;
    }
    super.onInternalKeyDown(event);
    if (isFromWidget) {
      return;
    }
    if (validKeys[event.key]) {
      event.preventDefault();
    }
    const el = this.element, active = DomHelper.getActiveElement(el);
    this.navigateFrom(active !== el && el.contains(active) ? active : null, event.key, event);
  }
  navigateFrom(active, key, event) {
    var _a5, _b2;
    const me = this, { treeWalker } = me, item = active && me.getItem(active), enterSubMenu = me.rtl ? "ArrowLeft" : "ArrowRight", exitSubMenu = me.rtl ? "ArrowRight" : "ArrowLeft";
    let toActivate;
    switch (key) {
      case "ArrowUp":
        treeWalker.currentNode = active || (active = me.bottomFocusTrap);
        treeWalker.previousNode();
        toActivate = treeWalker.currentNode;
        (_a5 = item == null ? void 0 : item.menu) == null ? void 0 : _a5.hide(false);
        break;
      case "ArrowDown":
        treeWalker.currentNode = active || (active = me.topFocusTrap);
        treeWalker.nextNode();
        toActivate = treeWalker.currentNode;
        (_b2 = item == null ? void 0 : item.menu) == null ? void 0 : _b2.hide(false);
        break;
      case " ":
        if (active && !active.classList.contains("b-disabled")) {
          if (item == null ? void 0 : item.menu) {
            me.openSubMenu(active, item);
          } else {
            me.triggerElement(active, event);
          }
        }
        break;
      case enterSubMenu:
        if (active && (item == null ? void 0 : item.menu) && !active.classList.contains("b-disabled")) {
          const openedMenu = me.openSubMenu(active, item);
          openedMenu == null ? void 0 : openedMenu.focus();
        } else {
          treeWalker.currentNode = active || (active = me.topFocusTrap);
          treeWalker.nextNode();
          toActivate = treeWalker.currentNode;
        }
        break;
      case exitSubMenu:
        if (me.isSubMenu) {
          me.hide();
        } else if (!active) {
          treeWalker.currentNode = active || (active = me.topFocusTrap);
          treeWalker.nextNode();
          toActivate = treeWalker.currentNode;
        }
        break;
      case "Enter":
        if (active && !active.classList.contains("b-disabled")) {
          me.triggerElement(active, event);
        }
        break;
    }
    if (toActivate) {
      if (toActivate === me.element) {
        me.navigateFrom(me.bottomFocusTrap, "ArrowUp", event);
      } else if (toActivate === active) {
        me.navigateFrom(me.topFocusTrap, "ArrowDown", event);
      } else {
        toActivate.focus();
      }
    }
  }
  //endregion
  //region Activate menu item
  getItem(item) {
    if (item != null) {
      if (typeof item === "number") {
        return this.items[item];
      }
      if (item.nodeType === Element.ELEMENT_NODE) {
        return Widget.fromElement(item, "menuitem", this.contentElement);
      }
      return this.items.find((c) => c.id == item);
    }
  }
  /**
   * Activate a menu item (from its element)
   * @private
   * @fires item
   * @param menuItemElement
   */
  triggerElement(menuItemElement, event) {
    const item = this.getItem(menuItemElement);
    if (item && !item.disabled) {
      item.doAction(event);
    }
  }
  /**
   * Returns true if this menu is a sub menu.
   * To find out which menu is the parent, check {@link #property-parentMenu}.
   * @type {Boolean}
   * @readonly
   */
  get isSubMenu() {
    var _a5;
    return this === ((_a5 = this.owner) == null ? void 0 : _a5.menu);
  }
  /**
   * Opens a submenu anchored to a menu item
   * @private
   * @param element
   * @param item
   */
  openSubMenu(element, item) {
    var _a5;
    const me = this, subMenu = item.menu;
    if (subMenu) {
      if (!subMenu.isVisible) {
        const event = { item, element };
        if (me.trigger("beforeSubMenu", event) === false) {
          return;
        }
        if (((_a5 = item.onBeforeSubMenu) == null ? void 0 : _a5.call(item, event)) === false) {
          return;
        }
        subMenu.show();
        if (subMenu.isVisible && !subMenu.isMenu) {
          me.currentSubMenu = subMenu;
        }
      }
      return subMenu;
    }
  }
  /**
   * Get/set focused menu item.
   * Shows submenu if newly focused item has a menu and is not disabled.
   * @property {HTMLElement}
   */
  set selectedElement(element) {
    const me = this, lastSelected = me._selectedElement;
    if (lastSelected) {
      const lastItem = me.getItem(lastSelected), lastItemMenu = lastItem == null ? void 0 : lastItem.menu;
      lastItemMenu == null ? void 0 : lastItemMenu.hide();
      lastSelected.classList.remove("b-active");
    }
    me._selectedElement = element;
    if (element) {
      const doFocus = DomHelper.isFocusable(element);
      element.classList.add("b-active");
      me.scrollable.scrollIntoView(element, {
        animate: !doFocus,
        focus: doFocus
      });
    }
  }
  get selectedElement() {
    return this._selectedElement;
  }
  selectFirst() {
    const treeWalker = this.treeWalker;
    treeWalker.currentNode = this.topFocusTrap;
    treeWalker.nextNode();
    this.requestAnimationFrame(() => treeWalker.currentNode.focus());
  }
  //endregion
  //region Close
  /**
   * Gets the parent Menu if this Menu is a submenu, or `undefined`.
   * @property {Core.widget.Menu}
   */
  get parentMenu() {
    var _a5;
    const result = this.owner;
    return result && (result.isMenu ? result : (_a5 = result.up) == null ? void 0 : _a5.call(result, "menu"));
  }
  /**
   * Gets this menus root menu, the very first menu shown in a sub menu hierarchy
   * @property {Core.widget.Menu}
   * @private
   */
  get rootMenu() {
    let menu = this;
    while (menu.parentMenu instanceof this.constructor) {
      menu = menu.parentMenu;
    }
    return menu;
  }
  //endregion
};
//region Hidden configs
/**
 * @hideConfigs scrollable
 */
/**
 * @hideproperties scrollable
 */
//endregion
//region Config
__publicField(Menu, "$name", "Menu");
__publicField(Menu, "type", "menu");
__publicField(Menu, "configurable", {
  layout: "vbox",
  focusable: true,
  draggable: false,
  showAnimation: null,
  hideAnimation: {
    opacity: {
      from: 1,
      to: 0,
      duration: ".15s",
      delay: "0s"
    }
  },
  align: {
    align: "t-b"
  },
  scrollAction: "hide",
  /**
   * Specify false to prevent the menu from getting focus when hovering items
   * @default
   * @config {Boolean}
   */
  focusOnHover: null,
  // We do need a Scroller so that we can use its API to scroll around.
  // But the overflow flags default to false.
  scrollable: false,
  defaultType: "menuitem",
  tools: {
    // To get rid of the close tool from Popup
    close: false
  },
  role: "menu",
  closeMenuDelay: 200,
  testConfig: {
    hideAnimation: null,
    closeMenuDelay: 1
  }
});
Menu.initClass();
Menu._$name = "Menu";

// ../Core/lib/Core/feature/base/ContextMenuBase.js
var contextMenuKeyEvent;
var ContextMenuBase = class extends InstancePlugin {
  //region Config
  static get configurable() {
    return {
      /**
       * This is a type of the context menu used to generate correct names for methods and events.
       * Should be in camel case. Required to be set in subclass.
       * @config {String}
       * @readonly
       */
      type: null,
      /**
       * Gets the Menu instance that this feature is using.
       * @member {Core.widget.Menu} menu
       * @readonly
       */
      /**
       * A config which will be applied when creating the Menu component.
       * @config {MenuConfig}
       */
      menu: {
        $config: ["lazy", "nullify"],
        value: {
          type: "menu",
          autoShow: false,
          closeAction: "hide",
          constrainTo: globalThis,
          scrollAction: null
        }
      },
      /**
       * {@link Core/widget/Menu} items object containing named child menu items to apply to the feature's
       * provided context menu.
       *
       * This may add extra items as below, but may also remove any of the default items by configuring the name
       * of the item as `null`.
       *
       * ```javascript
       * features : {
       *     cellMenu : {
       *         // This object is applied to the Feature's predefined default items
       *         items : {
       *             switchToDog : {
       *                 text : 'Dog',
       *                 icon : 'b-fa b-fa-fw b-fa-dog',
       *                 onItem({record}) {
       *                     record.dog = true;
       *                     record.cat = false;
       *                 },
       *                 weight : 500     // Make this second from end
       *             },
       *             switchToCat : {
       *                 text : 'Cat',
       *                 icon : 'b-fa b-fa-fw b-fa-cat',
       *                 onItem({record}) {
       *                     record.dog = false;
       *                     record.cat = true;
       *                 },
       *                 weight : 510     // Make this sink to end
       *             },
       *             add : null // We do not want the "Add" submenu to be available
       *         }
       *     }
       * }
       * ```
       *
       * @config {Object<String,MenuItemConfig|Boolean|null>}
       */
      items: {},
      /**
       * Event which is used to show context menu.
       * Available options are: 'contextmenu', 'click' and 'dblclick'.
       * Default value is used from {@link Grid/view/GridBase#config-contextMenuTriggerEvent}
       * @config {'contextmenu'|'click'|'dblclick'|null}
       */
      triggerEvent: null,
      /**
       * A CSS selector targeting an element, such as an ellipsis icon that when
       * clicked will trigger the menu to show.
       * @config {String}
       */
      clickTriggerSelector: null,
      /**
       * Set to `true` to prevent the native menu from showing when there are no menu items to show, or
       * you manually prevent the menu from showing in an event listener.
       * @prp {Boolean}
       */
      preventNativeMenu: null,
      /**
       * See {@link #keyboard-shortcuts Keyboard shortcuts} for details
       * @config {Object<String,KeyMapConfig>}
       */
      keyMap: {
        " ": { handler: "showContextMenuByKey", weight: 100 },
        "Ctrl+Space": "showContextMenuByKey"
      }
    };
  }
  // Plugin configuration. This plugin chains some of the functions in Grid.
  // The contextmenu event is emulated from a taphold gesture on touch platforms.
  static get pluginConfig() {
    return {
      assign: ["showContextMenu"],
      chain: [
        "onElementContextMenu",
        "onElementClick",
        "onElementDblClick"
      ]
    };
  }
  //endregion
  //region Init
  construct(...args) {
    var _a5;
    super.construct(...args);
    if (!((_a5 = this.type) == null ? void 0 : _a5.length)) {
      throw new Error(`Config 'type' is required to be specified for context menu`);
    }
  }
  //endregion
  //region Events
  /**
   * This event fires on the owning widget when an item is selected in the context menu.
   * @event contextMenuItem
   * @on-owner
   * @param {Core.widget.Widget} source The owning widget
   * @param {Core.widget.Menu} menu The menu
   * @param {Core.widget.MenuItem} item Selected menu item
   */
  /**
   * This event fires on the owning widget when a check item is toggled in the context menu.
   * @event contextMenuToggleItem
   * @on-owner
   * @param {Core.widget.Widget} source The owning widget
   * @param {Core.widget.Menu} menu The menu
   * @param {Core.widget.MenuItem} item Selected menu item
   * @param {Boolean} checked Checked or not
   */
  onElementContextMenu(event) {
    this.onElementEvent(event);
  }
  onElementClick(event) {
    this.onElementEvent(event);
  }
  onElementDblClick(event) {
    this.onElementEvent(event);
  }
  onElementEvent(event) {
    if (!event.handled && this.triggerEvent === event.type) {
      if (event.ctrlKey === true && !BrowserHelper.isMac) {
        event.preventDefault();
        return;
      }
      const originalEvent = event;
      if (contextMenuKeyEvent) {
        const { target } = contextMenuKeyEvent;
        event = EventHelper.fixEvent(new MouseEvent(event.type, event));
        Object.defineProperties(event, {
          target: { value: target },
          offsetX: { value: originalEvent.offsetX },
          offsetY: { value: originalEvent.offsetY }
        });
        event.preventDefault = function() {
          originalEvent.preventDefault();
          originalEvent.handled = true;
        };
      }
      this.internalShowContextMenu(event);
    } else if (event.type === "click" && this.clickTriggerSelector && event.target.matches(this.clickTriggerSelector)) {
      this.internalShowContextMenu(event, { align: "l-r", target: event.target });
    }
  }
  showContextMenuByKey(event) {
    if (!DomHelper.isEditable(event.target)) {
      const { target } = event;
      if (target) {
        contextMenuKeyEvent = event;
        DomHelper.triggerMouseEvent(target, this.triggerEvent);
        event.handled = true;
        event.preventDefault();
        event.stopImmediatePropagation();
        contextMenuKeyEvent = null;
        return true;
      }
    }
    return false;
  }
  //endregion
  //region Menu handlers
  internalShowContextMenu(domEvent, alignSpec) {
    const me = this;
    let data;
    if (me.disabled) {
      return;
    }
    if (!domEvent.handled && !domEvent.defaultPrevented && (data = me.getDataFromEvent(domEvent)) && me.shouldShowMenu(data)) {
      if (domEvent.type === "contextmenu" && BrowserHelper.isSafari && !domEvent.ctrlKey && domEvent.metaKey) {
        EventHelper.on({
          element: domEvent.target,
          capture: true,
          once: true,
          thisObj: this,
          click(e) {
            e.preventDefault();
            e.stopImmediatePropagation();
          }
        });
      }
      data.domEvent = domEvent;
      me.showContextMenu(data, alignSpec);
    }
  }
  getDataFromEvent(event) {
    return {
      feature: this,
      event,
      targetElement: this.getTargetElementFromEvent(event)
    };
  }
  getTargetElementFromEvent(event) {
    return event.target;
  }
  /**
   * Shows the context menu.
   * @param {Event} event The initiating event.
   * @param {AlignSpec|HTMLElement} [alignSpec] Menu alignment specification, or an element to align to
   * @on-owner
   */
  async showContextMenu(event, alignSpec) {
    var _a5, _b2, _c2;
    const me = this, isDOMEvent = DomHelper.isDOMEvent(event), menuContext = isDOMEvent ? me.getDataFromEvent(event) : event, domEvent = isDOMEvent ? event : menuContext.domEvent, elCenter = (_a5 = DomHelper.isInView(menuContext.targetElement)) == null ? void 0 : _a5.center;
    (_b2 = me._menu) == null ? void 0 : _b2.hide();
    if (me.disabled || !elCenter) {
      return;
    }
    me.menuContext = menuContext;
    const {
      type,
      client,
      processItems
    } = me;
    Objects.assign(menuContext, {
      point: (domEvent == null ? void 0 : domEvent.clientX) ? [domEvent.clientX + 1, domEvent.clientY + 1] : [elCenter.x, elCenter.y],
      menu: me,
      items: {},
      selection: client.selectedRecords
    });
    me.callChainablePopulateMenuMethod(menuContext);
    Objects.merge(menuContext.items, me.baseItems);
    if (me.preventNativeMenu) {
      me.preventDefaultEvent(menuContext);
    }
    if ((!processItems || await me.callback(processItems, client, [menuContext]) !== false) && me.hasActiveMenuItems(menuContext)) {
      me.populateItemsWithData(menuContext);
      if (me.beforeContextMenuShow(menuContext) !== false) {
        const { menu } = me, { featureItems } = menu, { items: items2 } = menuContext;
        me.preventDefaultEvent(menuContext);
        let result = client.trigger(`${type}MenuBeforeShow`, menuContext);
        if (ObjectHelper.isPromise(result)) {
          domEvent.handled = true;
          result = await result;
        }
        if (!Object.values(items2).some((val) => val)) {
          return;
        }
        if (result !== false) {
          if (featureItems) {
            menu.remove(featureItems);
            ArrayHelper.asArray(featureItems).forEach((i) => i.destroy());
          }
          menu.featureItems = menu.add(...Array.isArray(items2) ? items2 : menu.processItemsObject(items2));
          menu.showBy(alignSpec || {
            target: menuContext.domEvent,
            align: "s0-e100"
          });
          (_c2 = me.touchMoveDetacher) == null ? void 0 : _c2.call(me);
          me.touchMoveDetacher = EventHelper.on({
            element: client.element,
            touchmove: () => menu.hide(),
            thisObj: menu,
            once: true
          });
          if (domEvent) {
            domEvent.preventDefault();
            domEvent.handled = true;
          }
        }
      }
    }
  }
  /**
   * Returns the base, configured-in menu items set from the configured items, taking into
   * account the namedItems the feature offers.
   * @property {Object[]}
   * @readonly
   * @internal
   */
  get baseItems() {
    if (!this._baseItems) {
      const me = this, { namedItems } = me, baseItems = me._baseItems = Objects.assign({}, me.items);
      for (const ref in baseItems) {
        const item = baseItems[ref];
        if (item) {
          if (namedItems && ref in namedItems) {
            baseItems[ref] = typeof item === "object" ? Objects.merge(Objects.clone(namedItems[ref]), item) : namedItems[ref];
          } else if (item === true) {
            delete baseItems[ref];
          }
        }
      }
    }
    return this._baseItems;
  }
  /**
   * Hides the context menu
   * @internal
   */
  hideContextMenu(animate) {
    var _a5;
    (_a5 = this.menu) == null ? void 0 : _a5.hide(animate);
  }
  callChainablePopulateMenuMethod(eventParams) {
    var _a5, _b2;
    (_b2 = (_a5 = this.client)[`populate${StringHelper.capitalize(this.type)}Menu`]) == null ? void 0 : _b2.call(_a5, eventParams);
  }
  hasActiveMenuItems(eventParams) {
    return Object.values(eventParams.items).some((item) => item && !item.hidden);
  }
  /**
   * Override this function and return `false` to prevent the context menu from being shown. Returns `true` by default.
   * Returning false will by default make the native OS menu show, unless you configure {@link #config-preventNativeMenu}
   * @returns {Boolean}
   * @internal
   */
  shouldShowMenu() {
    return true;
  }
  beforeContextMenuShow(eventParams) {
  }
  populateItemsWithData(eventParams) {
  }
  preventDefaultEvent(eventParams) {
    var _a5;
    (_a5 = eventParams.event) == null ? void 0 : _a5.preventDefault();
  }
  //endregion
  //region Configurables
  get triggerEvent() {
    return this._triggerEvent || this.client.contextMenuTriggerEvent;
  }
  changeMenu(menu, oldMenu) {
    const me = this, {
      client,
      type
    } = me;
    if (menu) {
      return Menu.reconfigure(oldMenu, menu ? Menu.mergeConfigs({
        owner: client,
        rootElement: client.rootElement,
        onItem(itemEvent) {
          client.trigger(`${type}MenuItem`, itemEvent);
        },
        onToggle(itemEvent) {
          client.trigger(`${type}MenuToggleItem`, itemEvent);
        },
        onDestroy() {
          me.menu = null;
        },
        // Load up the item event with the contextual info
        onBeforeItem(itemEvent) {
          Object.assign(itemEvent, me.menuContext);
        },
        onShow({ source: menu2 }) {
          me.menuContext.menu = menu2;
          client.trigger(`${type}MenuShow`, me.menuContext);
        }
      }, menu) : null, me);
    } else if (oldMenu == null ? void 0 : oldMenu.isWidget) {
      oldMenu.destroy();
    }
  }
  //endregion
};
__publicField(ContextMenuBase, "$name", "ContextMenuBase");
ContextMenuBase._$name = "ContextMenuBase";

// ../Core/lib/Core/helper/ResizeHelper.js
var documentListeners = {
  down: "onMouseDown",
  move: "onMouseMove",
  up: "onMouseUp",
  docclick: "onDocumentClick",
  touchstart: {
    handler: "onTouchStart",
    // We preventDefault touchstart so as not to scroll. Must not be passive.
    // https://developers.google.com/web/updates/2017/01/scrolling-intervention
    passive: false
  },
  touchmove: "onTouchMove",
  touchend: "onTouchEnd",
  keydown: "onKeyDown"
};
var ResizeHelper = class extends Events_default(Base) {
  //region Config
  static get defaultConfig() {
    return {
      /**
       * CSS class added when resizing
       * @config {String}
       * @default
       */
      resizingCls: "b-resizing",
      /**
       * The amount of pixels to move mouse before it counts as a drag operation
       * @config {Number}
       * @default
       */
      dragThreshold: 5,
      /**
       * Resizing handle size
       * @config {Number}
       * @default
       */
      handleSize: 10,
      /**
       * Automatically shrink virtual handles when available space < handleSize. The virtual handles will
       * decrease towards width/height 1, reserving space between opposite handles to for example leave room for
       * dragging. To configure reserved space, see {@link #config-reservedSpace}.
       * @config {Boolean}
       * @default false
       */
      dynamicHandleSize: null,
      /**
       * Room in px to leave unoccupied by handles when shrinking them dynamically (see
       * {@link #config-dynamicHandleSize}).
       * @config {Number}
       * @default
       */
      reservedSpace: 10,
      /**
       * Resizing handle size on touch devices
       * @config {Number}
       * @default
       */
      touchHandleSize: 30,
      /**
       * Minimum width when resizing
       * @config {Number}
       * @default
       */
      minWidth: 1,
      /**
       * Max width when resizing.
       * @config {Number}
       * @default
       */
      maxWidth: 0,
      /**
       * Minimum height when resizing
       * @config {Number}
       * @default
       */
      minHeight: 1,
      /**
       * Max height when resizing
       * @config {Number}
       * @default
       */
      maxHeight: 0,
      // outerElement, attach events to it and use as outer limit when looking for ancestors
      outerElement: document.body,
      /**
       * Optional scroller used to read scroll position. If unspecified, the outer element will be used.
       * @config {Core.helper.util.Scroller}
       */
      scroller: null,
      /**
       * Assign a function to determine if a hovered element can be resized or not.
       * Return `true` to allow resizing or `false` to prevent.
       * @config {Function}
       * @param {HTMLElement} element
       * @returns {Boolean}
       * @default
       */
      allowResize: null,
      /**
       * Outer element that limits where element can be dragged
       * @config {HTMLElement}
       * @default
       */
      dragWithin: null,
      /**
       * A function that determines if dragging an element is allowed. Gets called with the element as argument,
       * return `true` to allow dragging or `false` to prevent.
       * @config {Function}
       * @param {HTMLElement} element
       * @returns {Boolean}
       * @default
       */
      isElementResizable: null,
      /**
       * A CSS selector used to determine if resizing an element is allowed.
       * @config {String}
       * @default
       */
      targetSelector: null,
      /**
       * Use left handle when resizing. Only applies when `direction` is 'horizontal'
       * @config {Boolean}
       * @default
       */
      leftHandle: true,
      /**
       * Use right handle when resizing. Only applies when `direction` is 'horizontal'
       * @config {Boolean}
       * @default
       */
      rightHandle: true,
      /**
       * Use top handle when resizing. Only applies when `direction` is 'vertical'
       * @config {Boolean}
       * @default
       */
      topHandle: true,
      /**
       * Use bottom handle when resizing. Only applies when `direction` is 'vertical'
       * @config {Boolean}
       * @default
       */
      bottomHandle: true,
      /**
       * A CSS selector used to determine where handles should be "displayed" when resizing. Defaults to
       * targetSelector if unspecified
       * @config {String}
       */
      handleSelector: null,
      /**
       * A CSS selector used to determine which inner element contains handles.
       * @config {String}
       */
      handleContainerSelector: null,
      startEvent: null,
      /*
       * Optional config object, used by EventResize feature: it appends proxy and has to start resizing immediately
       * @config {Object}
       * @private
       */
      grab: null,
      /**
       * CSS class added when the resize state is invalid
       * @config {String}
       * @default
       */
      invalidCls: "b-resize-invalid",
      // A number that controls whether or not the element is wide enough for it to make sense to show resize handles
      // e.g. handle width is 10px, so doesn't make sense to show them unless handles on both sides fit
      handleVisibilityThreshold: null,
      // Private config that disables translation when resizing left edge. Useful for example in cases when element
      // being resized is part of a flex layout
      skipTranslate: false,
      /**
       * Direction to resize in, either 'horizontal' or 'vertical'
       * @config {'horizontal'|'vertical'}
       * @default
       */
      direction: "horizontal",
      clickSwallowDuration: 50,
      constrainToParent: true,
      rtlSource: null
    };
  }
  //endregion
  //region Events
  /**
   * Fired while dragging
   * @event resizing
   * @param {Core.helper.ResizeHelper} source
   * @param {ResizeContext} context Resize context
   * @param {MouseEvent} event Browser event
   */
  /**
   * Fired when dragging starts.
   * @event resizeStart
   * @param {Core.helper.ResizeHelper} source
   * @param {ResizeContext} context Resize context
   * @param {MouseEvent|TouchEvent} event Browser event
   */
  /**
   * Fires after resize, and allows for asynchronous finalization by setting 'async' to `true` on the context object.
   * @event resize
   * @param {Core.helper.ResizeHelper} source
   * @param {ResizeContext} context Context about the resize operation. Set 'async' to `true` to indicate asynchronous
   * validation of the resize flow (for showing a confirmation dialog etc)
   */
  /**
   * Fires when a resize is canceled (width & height are reverted)
   * @event cancel
   * @param {Core.helper.ResizeHelper} source
   * @param {ResizeContext} context Resize context
   * @param {MouseEvent|TouchEvent} event Browser event
   */
  //endregion
  //region Init
  construct(config) {
    const me = this;
    super.construct(config);
    if (!me.handleSelector && !BrowserHelper.isHoverableDevice) {
      me.handleSize = me.touchHandleSize;
    }
    me.handleVisibilityThreshold = me.handleVisibilityThreshold || 2 * me.handleSize;
    me.initListeners();
    me.initResize();
  }
  doDestroy() {
    this.abort(true);
    super.doDestroy();
  }
  updateSkipUpdatingElement(skip) {
    if (skip) {
      this.skipTranslate = true;
    }
  }
  getX(element) {
    if (this.positioning === "inset") {
      return parseFloat(getComputedStyle(element).insetInlineStart, 10);
    }
    return DomHelper.getTranslateX(element);
  }
  getY(element) {
    if (this.positioning === "inset") {
      return parseFloat(getComputedStyle(element).insetBlockStart, 10);
    }
    return DomHelper.getTranslateY(element);
  }
  setX(element, x) {
    if (this.positioning === "inset") {
      element.style.insetInlineStart = x + "px";
    } else {
      DomHelper.setTranslateX(element, x);
    }
  }
  setY(element, y) {
    if (this.positioning === "inset") {
      element.style.insetBlockStart = y + "px";
    } else {
      DomHelper.setTranslateY(element, y);
    }
  }
  /**
   * Initializes resizing
   * @private
   */
  initResize() {
    const me = this;
    if (!me.isElementResizable && me.targetSelector) {
      me.isElementResizable = (element) => element.closest(me.targetSelector);
    }
    if (me.grab) {
      const { edge, element, event } = me.grab;
      me.startEvent = event;
      const elementStartX = me.getX(element) || element.offsetLeft, elementStartY = me.getY(element) || element.offsetTop;
      me.context = {
        element,
        edge,
        valid: true,
        async: false,
        elementStartX,
        elementStartY,
        newX: elementStartX,
        // No change yet on start, but info must be present
        newY: elementStartY,
        // No change yet on start, but info must be present
        elementWidth: element.offsetWidth,
        elementHeight: element.offsetHeight,
        startX: event.clientX + me.scrollLeft,
        startY: event.clientY + me.scrollTop,
        finalize: () => {
          var _a5;
          return (_a5 = me.reset) == null ? void 0 : _a5.call(me);
        }
      };
      element.classList.add(me.resizingCls);
      me.internalStartResize(me.isTouch);
    }
  }
  /**
   * Initialize listeners
   * @private
   */
  initListeners() {
    const me = this, dragStartListeners = {
      element: me.outerElement,
      mousedown: documentListeners.down,
      touchstart: documentListeners.touchstart,
      thisObj: me
    };
    if (!me.handleSelector && BrowserHelper.isHoverableDevice) {
      dragStartListeners.mousemove = {
        handler: documentListeners.move,
        // Filter events for checkResizeHandles so we only get called if the mouse
        // is over one of our targets.
        delegate: me.targetSelector
      };
      dragStartListeners.mouseleave = {
        handler: "onMouseLeaveTarget",
        delegate: me.targetSelector,
        capture: true
      };
    }
    EventHelper.on(dragStartListeners);
  }
  get isRTL() {
    var _a5;
    return Boolean((_a5 = this.rtlSource) == null ? void 0 : _a5.rtl);
  }
  //endregion
  //region Scroll helpers
  get scrollLeft() {
    if (this.scroller) {
      return this.scroller.x;
    }
    return this.outerElement.scrollLeft;
  }
  get scrollTop() {
    if (this.scroller) {
      return this.scroller.y;
    }
    return this.outerElement.scrollTop;
  }
  //endregion
  //region Events
  internalStartResize(isTouch) {
    var _a5;
    const dragListeners = {
      element: document,
      keydown: documentListeners.keydown,
      thisObj: this
    };
    if (isTouch) {
      dragListeners.touchmove = documentListeners.touchmove;
      dragListeners.touchend = dragListeners.pointerup = documentListeners.touchend;
    } else {
      dragListeners.mousemove = documentListeners.move;
      dragListeners.mouseup = documentListeners.up;
    }
    this.removeDragListeners = EventHelper.on(dragListeners);
    (_a5 = this.onResizeHandlePointerDown) == null ? void 0 : _a5.call(this, this.startEvent);
  }
  // Empty class implementation. If listeners *are* added, the detacher is added
  // as an instance property. So this is always callable.
  removeDragListeners() {
  }
  reset() {
    var _a5;
    (_a5 = this.removeDragListeners) == null ? void 0 : _a5.call(this);
    this.context = null;
    this.trigger("reset");
  }
  canResize(element, event) {
    return !this.isElementResizable || this.isElementResizable(element, event);
  }
  onPointerDown(isTouch, event) {
    const me = this;
    me.startEvent = event;
    if (me.canResize(event.target, event) && me.grabResizeHandle(isTouch, event)) {
      event.stopImmediatePropagation();
      if (event.type === "touchstart") {
        event.preventDefault();
      }
      me.internalStartResize(isTouch);
    }
  }
  onTouchStart(event) {
    if (event.touches.length > 1) {
      return;
    }
    this.onPointerDown(true, event);
  }
  /**
   * Grab draggable element on mouse down.
   * @private
   * @param {MouseEvent|PointerEvent} event
   */
  onMouseDown(event) {
    if (event.button !== 0) {
      return;
    }
    this.onPointerDown(false, event);
  }
  internalMove(isTouch, event) {
    var _a5;
    const me = this, { context, direction } = me;
    if ((context == null ? void 0 : context.element) && (context.started || EventHelper.getDistanceBetween(me.startEvent, event) >= me.dragThreshold)) {
      if (!context.started) {
        (_a5 = me.scrollManager) == null ? void 0 : _a5.startMonitoring(ObjectHelper.merge({
          scrollables: [
            {
              element: me.dragWithin || me.outerElement,
              direction
            }
          ],
          callback: (config) => {
            var _a6;
            return ((_a6 = me.context) == null ? void 0 : _a6.element) && me.lastMouseMoveEvent && me.update(me.lastMouseMoveEvent, config);
          }
        }, me.monitoringConfig));
        me.trigger("resizeStart", { context, event });
        context.started = true;
      }
      me.update(event);
    } else if (!isTouch && !me.handleSelector) {
      me.checkResizeHandles(event);
    }
  }
  onTouchMove(event) {
    this.internalMove(true, event);
  }
  /**
   * Move grabbed element with mouse.
   * @param {MouseEvent|PointerEvent} event
   * @fires resizestart
   * @private
   */
  onMouseMove(event) {
    this.internalMove(false, event);
  }
  onPointerUp(isTouch, event) {
    var _a5, _b2, _c2;
    const me = this, context = me.context;
    (_a5 = me.removeDragListeners) == null ? void 0 : _a5.call(me);
    if (context) {
      (_b2 = me.scrollManager) == null ? void 0 : _b2.stopMonitoring();
      if (context.started) {
        EventHelper.on({
          element: DomHelper.getRootElement(event.target),
          thisObj: me,
          click: documentListeners.docclick,
          expires: me.clickSwallowDuration,
          // In case a click did not ensue, remove the listener
          capture: true,
          once: true
        });
      }
      me.finishResize(event);
    } else {
      (_c2 = me.reset) == null ? void 0 : _c2.call(me);
    }
  }
  onTouchEnd(event) {
    this.onPointerUp(true, event);
  }
  /**
   * Drop on mouse up (if dropped on valid target).
   * @param {MouseEvent|PointerEvent} event
   * @private
   */
  onMouseUp(event) {
    this.onPointerUp(false, event);
  }
  /**
   * This is a capture listener, only added during drag, which prevents a click gesture
   * propagating from the terminating mouseup gesture
   * @param {MouseEvent} event
   * @private
   */
  onDocumentClick(event) {
    event.stopPropagation();
  }
  /**
   * Cancel on ESC key
   * @param {KeyboardEvent} event
   * @private
   */
  onKeyDown(event) {
    if (event.key === "Escape") {
      this.abort();
    }
  }
  //endregion
  //region Grab, update, finish
  /**
   * Updates resize, called when an element is grabbed and mouse moves
   * @private
   * @fires resizing
   */
  update(event) {
    const me = this, { context, constrainToParent } = me, { element } = context;
    if (constrainToParent) {
      const parentRectangle = Rectangle.from(me.outerElement);
      context.currentX = Math.max(Math.min(event.clientX, parentRectangle.right), parentRectangle.x) + me.scrollLeft;
      context.currentY = Math.max(Math.min(event.clientY, parentRectangle.bottom), parentRectangle.y) + me.scrollTop;
    } else {
      context.currentX = event.clientX + me.scrollLeft;
      context.currentY = event.clientY + me.scrollTop;
    }
    element.classList.add(me.resizingCls);
    if (me.updateResize(event)) {
      me.trigger("resizing", { context, event });
      element.classList.toggle(me.invalidCls, context.valid === false);
    }
    me.lastMouseMoveEvent = event;
  }
  /**
   * Abort dragging
   */
  abort(silent = false) {
    var _a5, _b2;
    const me = this;
    (_b2 = (_a5 = me.scrollManager) == null ? void 0 : _a5.stopMonitoring) == null ? void 0 : _b2.call(_a5);
    if (me.context) {
      me.abortResize(null, silent);
    } else if (!me.isDestroyed) {
      me.reset();
    }
  }
  /**
   * Starts resizing, updates ResizeHelper#context with relevant info.
   * @private
   * @param {Boolean} isTouch
   * @param {MouseEvent} event
   * @returns {Boolean} True if handled, false if not
   */
  grabResizeHandle(isTouch, event) {
    var _a5;
    const me = this;
    if (me.allowResize && !me.allowResize(event.target, event)) {
      return false;
    }
    const handleSelector = me.handleSelector, coordsFrom = event.type === "touchstart" ? event.changedTouches[0] : event, clientX = coordsFrom.clientX, clientY = coordsFrom.clientY;
    let element = me.targetSelector ? event.target.closest(me.targetSelector) : event.target;
    if (element) {
      let edge;
      if (handleSelector) {
        if (event.target.matches(handleSelector)) {
          if (me.direction === "horizontal") {
            if (event.pageX < DomHelper.getPageX(element) + element.offsetWidth / 2) {
              edge = me.isRTL ? "right" : "left";
            } else {
              edge = me.isRTL ? "left" : "right";
            }
          } else {
            if (event.pageY < DomHelper.getPageY(element) + element.offsetHeight / 2) {
              edge = "top";
            } else {
              edge = "bottom";
            }
          }
        } else {
          return false;
        }
      } else {
        if (me.direction === "horizontal") {
          if (me.overLeftHandle(event, element)) {
            edge = me.isRTL ? "right" : "left";
          } else if (me.overRightHandle(event, element)) {
            edge = me.isRTL ? "left" : "right";
          }
        } else {
          if (me.overTopHandle(event, element)) {
            edge = "top";
          } else if (me.overBottomHandle(event, element)) {
            edge = "bottom";
          }
        }
        if (!edge) {
          me.context = null;
          return false;
        }
      }
      if (event.type === "touchstart") {
        event.preventDefault();
      }
      const mutatedContext = (_a5 = me.internalBeforeStart) == null ? void 0 : _a5.call(me, { element, edge });
      if (mutatedContext) {
        element = mutatedContext.element;
        edge = mutatedContext.edge;
      }
      if (me.trigger("beforeResizeStart", { element, event }) !== false) {
        const elementStartX = me.getX(element) || element.offsetLeft, elementStartY = me.getY(element) || element.offsetTop;
        me.context = {
          element,
          edge,
          isTouch,
          valid: true,
          async: false,
          direction: me.direction,
          elementStartX,
          elementStartY,
          newX: elementStartX,
          // No change yet on start, but info must be present
          newY: elementStartY,
          // No change yet on start, but info must be present
          elementWidth: element.offsetWidth,
          elementHeight: element.offsetHeight,
          startX: clientX + me.scrollLeft,
          startY: clientY + me.scrollTop,
          finalize: () => {
            var _a6;
            return (_a6 = me.reset) == null ? void 0 : _a6.call(me);
          }
        };
        element.classList.add(me.resizingCls);
        return true;
      }
    }
    return false;
  }
  /**
   * Check if mouse is over a resize handle (virtual). If so, highlight.
   * @private
   * @param {MouseEvent} event
   */
  checkResizeHandles(event) {
    const me = this, target = me.targetSelector ? event.target.closest(me.targetSelector) : event.target;
    let over = false;
    if (target && (!me.allowResize || me.allowResize(event.target, event))) {
      me.currentElement = me.handleContainerSelector ? event.target.closest(me.handleContainerSelector) : event.target;
      if (me.currentElement) {
        if (me.direction === "horizontal") {
          over = me.overLeftHandle(event, target) || me.overRightHandle(event, target);
        } else {
          over = me.overTopHandle(event, target) || me.overBottomHandle(event, target);
        }
        if (over) {
          me.highlightHandle();
        } else {
          me.unHighlightHandle();
        }
      }
    } else if (me.currentElement) {
      me.unHighlightHandle();
    }
    return over;
  }
  onMouseLeaveTarget(event) {
    const me = this;
    me.currentElement = me.handleContainerSelector ? event.target.closest(me.handleContainerSelector) : event.target;
    if (me.currentElement) {
      me.unHighlightHandle();
    }
  }
  /**
   * Updates size of target (on mouse move).
   * @private
   * @param {MouseEvent|PointerEvent} event
   */
  updateResize(event) {
    const me = this, {
      context,
      allowEdgeSwitch,
      skipTranslate,
      skipUpdatingElement
    } = me;
    let updated;
    if (allowEdgeSwitch) {
      if (me.direction === "horizontal") {
        context.edge = context.currentX > context.startX ? "right" : "left";
      } else {
        context.edge = context.currentY > context.startY ? "bottom" : "top";
      }
    }
    const {
      element,
      elementStartX,
      elementStartY,
      elementWidth,
      elementHeight,
      edge
    } = context, { style } = element, deltaX = (context.currentX - context.startX) * (me.positioning === "inset" && me.isRTL ? -1 : 1), deltaY = context.currentY - context.startY, minWidth = DomHelper.getExtremalSizePX(element, "minWidth") || me.minWidth, maxWidth = DomHelper.getExtremalSizePX(element, "maxWidth") || me.maxWidth, minHeight = DomHelper.getExtremalSizePX(element, "minHeight") || me.minHeight, maxHeight = DomHelper.getExtremalSizePX(element, "maxHeight") || me.maxHeight, sign = edge === "right" && (!me.isRTL || me.positioning === "inset") || edge === "bottom" ? 1 : -1, newWidth = elementWidth + deltaX * sign, newHeight = elementHeight + deltaY * sign;
    let width = Math.max(minWidth, newWidth), height = Math.max(minHeight, newHeight);
    if (maxWidth > 0) {
      width = Math.min(width, maxWidth);
    }
    if (maxHeight > 0) {
      height = Math.min(height, maxHeight);
    }
    if (me.direction === "horizontal" && elementWidth !== width) {
      if (!skipUpdatingElement) {
        style.flex = "";
        style.width = Math.abs(width) + "px";
      }
      context.newWidth = width;
      if (edge === "left" || width < 0) {
        const newX = Math.max(Math.min(elementStartX + elementWidth - me.minWidth, elementStartX + deltaX), 0);
        if (!skipTranslate) {
          me.setX(element, Math.round(newX));
        }
        context.newX = newX;
      } else if (edge === "right" && allowEdgeSwitch && !skipTranslate) {
        me.setX(element, elementStartX);
      }
      updated = true;
    } else if (me.direction === "vertical" && elementHeight !== newHeight) {
      if (!skipUpdatingElement) {
        style.flex = "";
        style.height = Math.abs(height) + "px";
      }
      context.newHeight = height;
      if (edge === "top" || height < 0) {
        context.newY = Math.max(Math.min(elementStartY + elementHeight - me.minHeight, elementStartY + deltaY), 0);
        if (!skipTranslate) {
          me.setY(element, context.newY);
        }
      } else if (edge === "bottom" && allowEdgeSwitch && !skipTranslate) {
        me.setY(element, elementStartY);
      }
      updated = true;
    }
    return updated;
  }
  /**
   * Finalizes resize, fires drop.
   * @private
   * @param {MouseEvent|PointerEvent} event
   * @fires resize
   * @fires cancel
   */
  finishResize(event) {
    var _a5;
    const me = this, context = me.context, eventObject = { context, event };
    context.element.classList.remove(me.resizingCls);
    if (context.started) {
      let changed = false;
      if (me.direction === "horizontal") {
        changed = context.newWidth && context.newWidth !== context.elementWidth;
      } else {
        changed = context.newHeight && context.newHeight !== context.elementHeight;
      }
      me.trigger(changed ? "resize" : "cancel", eventObject);
      if (!context.async) {
        context.finalize();
      }
    } else {
      (_a5 = me.reset) == null ? void 0 : _a5.call(me);
    }
  }
  /**
   * Abort resizing
   * @private
   * @fires cancel
   */
  abortResize(event = null, silent = false) {
    const me = this, context = me.context;
    context.element.classList.remove(me.resizingCls);
    if (me.direction === "horizontal") {
      if (context.edge === "left" || context.allowEdgeSwitch && !context.skipTranslate) {
        me.setX(context.element, context.elementStartX);
      }
      context.element.style.width = context.elementWidth + "px";
    } else {
      me.setY(context.element, context.elementStartY);
      context.element.style.height = context.elementHeight + "px";
    }
    !silent && me.trigger("cancel", { context, event });
    if (!me.isDestroyed) {
      me.reset();
    }
  }
  //endregion
  //region Handles
  /**
   * Highlights handles (applies css that changes cursor).
   * @private
   */
  highlightHandle() {
    const me = this, target = me.targetSelector ? me.currentElement.closest(me.targetSelector) : me.currentElement;
    me.currentElement.classList.add("b-resize-handle");
    target.classList.add("b-over-resize-handle");
  }
  /**
   * Unhighlight handles (removes css).
   * @private
   */
  unHighlightHandle() {
    const me = this, target = me.targetSelector ? me.currentElement.closest(me.targetSelector) : me.currentElement;
    target && target.classList.remove("b-over-resize-handle");
    me.currentElement.classList.remove("b-resize-handle");
    me.currentElement = null;
  }
  overAnyHandle(event, target) {
    return this.overStartHandle(event, target) || this.overEndHandle(event, target);
  }
  overStartHandle(event, target) {
    return this.direction === "horizontal" ? this.overLeftHandle(event, target) : this.overTopHandle(event, target);
  }
  overEndHandle(event, target) {
    return this.direction === "horizontal" ? this.overRightHandle(event, target) : this.overBottomHandle(event, target);
  }
  getDynamicHandleSize(opposite, offsetWidth) {
    const handleCount = opposite ? 2 : 1, { handleSize } = this;
    if (this.dynamicHandleSize && handleSize * handleCount > offsetWidth - this.reservedSpace) {
      return Math.max(Math.floor((offsetWidth - this.reservedSpace) / handleCount), 0);
    }
    return handleSize;
  }
  /**
   * Check if over left handle (virtual).
   * @private
   * @param {MouseEvent} event MouseEvent
   * @param {HTMLElement} target The current target element
   * @returns {Boolean} Returns true if mouse is over left handle, otherwise false
   */
  overLeftHandle(event, target) {
    const me = this, { offsetWidth } = target;
    if (me.leftHandle && me.canResize(target, event) && (offsetWidth >= me.handleVisibilityThreshold || me.dynamicHandleSize)) {
      const leftHandle = Rectangle.from(target);
      leftHandle.width = me.getDynamicHandleSize(me.rightHandle, offsetWidth);
      return leftHandle.width > 0 && leftHandle.contains(EventHelper.getPagePoint(event));
    }
    return false;
  }
  /**
   * Check if over right handle (virtual).
   * @private
   * @param {MouseEvent} event MouseEvent
   * @param {HTMLElement} target The current target element
   * @returns {Boolean} Returns true if mouse is over left handle, otherwise false
   */
  overRightHandle(event, target) {
    const me = this, { offsetWidth } = target;
    if (me.rightHandle && me.canResize(target, event) && (offsetWidth >= me.handleVisibilityThreshold || me.dynamicHandleSize)) {
      const rightHandle = Rectangle.from(target);
      rightHandle.x = rightHandle.right - me.getDynamicHandleSize(me.leftHandle, offsetWidth);
      return rightHandle.width > 0 && rightHandle.contains(EventHelper.getPagePoint(event));
    }
    return false;
  }
  /**
   * Check if over top handle (virtual).
   * @private
   * @param {MouseEvent} event MouseEvent
   * @param {HTMLElement} target The current target element
   * @returns {Boolean} Returns true if mouse is over top handle, otherwise false
   */
  overTopHandle(event, target) {
    const me = this, { offsetHeight } = target;
    if (me.topHandle && me.canResize(target, event) && (offsetHeight >= me.handleVisibilityThreshold || me.dynamicHandleSize)) {
      const topHandle = Rectangle.from(target);
      topHandle.height = me.getDynamicHandleSize(me.bottomHandle, offsetHeight);
      return topHandle.height > 0 && topHandle.contains(EventHelper.getPagePoint(event));
    }
    return false;
  }
  /**
   * Check if over bottom handle (virtual).
   * @private
   * @param {MouseEvent} event MouseEvent
   * @param {HTMLElement} target The current target element
   * @returns {Boolean} Returns true if mouse is over bottom handle, otherwise false
   */
  overBottomHandle(event, target) {
    const me = this, { offsetHeight } = target;
    if (me.bottomHandle && me.canResize(target, event) && (offsetHeight >= me.handleVisibilityThreshold || me.dynamicHandleSize)) {
      const bottomHandle = Rectangle.from(target);
      bottomHandle.y = bottomHandle.bottom - me.getDynamicHandleSize(me.bottomHandle, offsetHeight);
      return bottomHandle.height > 0 && bottomHandle.contains(EventHelper.getPagePoint(event));
    }
    return false;
  }
  //endregion
};
__publicField(ResizeHelper, "configurable", {
  // Private config that disables updating elements width and position, for when data is live updated during
  // resize, leading to element being redrawn
  skipUpdatingElement: null
});
ResizeHelper._$name = "ResizeHelper";

// ../Core/lib/Core/helper/TemplateHelper.js
var tagSpaceRe = />[ \t\r\n]+</g;
var multiSpaceRe = /\s\s+/g;
var TemplateHelper = class _TemplateHelper {
  /**
   * Tag function for template literals that does some basic cleanup
   * @private
   */
  static tpl(strings, ...values) {
    let count = 0, output = values.map((val, i) => {
      let ret = strings[i];
      if (Array.isArray(val)) {
        ret += val.reduce((ack, item) => ack += (typeof item === "string" ? item.trim() : item) + "\n", "");
      } else {
        if (val === void 0) {
          val = "";
        }
        ret += typeof val === "string" ? val.trim() : val;
      }
      count++;
      return ret;
    }).join("");
    if (count <= strings.length) {
      output += strings[strings.length - 1];
    }
    return output.replace(tagSpaceRe, "><").replace(multiSpaceRe, " ").trim();
  }
  /**
   * Tag function for template literals that does some basic cleanup. Version for docs that do not remove blank space,
   * to keep code snippets formatting intact.
   * @private
   */
  static docsTpl(strings, ...values) {
    let count = 0, output = values.map((val, i) => {
      let ret = strings[i];
      if (Array.isArray(val)) {
        ret += val.reduce((ack, item) => ack += (typeof item === "string" ? item.trim() : item) + "\n", "");
      } else {
        if (val === void 0) {
          val = "";
        }
        ret += typeof val === "string" ? val.trim() : val;
      }
      count++;
      return ret;
    }).join("");
    if (count <= strings.length) {
      output += strings[strings.length - 1];
    }
    return output.replace(tagSpaceRe, "><").trim();
  }
  static repeat(times) {
    return function(...args) {
      return _TemplateHelper.tpl(...args).repeat(times);
    };
  }
  static loop(times) {
  }
};
TemplateHelper._$name = "TemplateHelper";

// ../Core/lib/Core/helper/TimeZoneHelper.js
var TimeZoneOffsetInfo = class extends Array {
  constructor(timeZone, year) {
    super();
    this.timeZone = timeZone;
    this.year = year;
  }
};
var TimeZoneDate = class {
  constructor(asString, timeZone) {
    this.asString = asString;
    this.timeZone = timeZone;
    this.asArray = parseStringDate(asString);
  }
  // Lazy, used in TZH.toTimeZone
  get asLocalDate() {
    if (!this._asLocalDate) {
      this._asLocalDate = new Date(...this.asArray);
    }
    return this._asLocalDate;
  }
  // Lazy, used when finding offsets
  get asTicksUtc() {
    if (!this._asTicksUtc) {
      this._asTicksUtc = new Date(Date.UTC(...this.asArray)).getTime();
    }
    return this._asTicksUtc;
  }
};
var toTimeZoneInternal = (date2, timeZone) => {
  const tzDateString = date2.toLocaleString("sv-SE", { timeZone });
  return new TimeZoneDate(tzDateString, timeZone);
};
var getOffsetUtc = (ticks, timeZone) => {
  const { asTicksUtc, asString } = toTimeZoneInternal(new Date(ticks), timeZone);
  return { offset: (ticks - asTicksUtc) / 6e4, tzTicksUtc: asTicksUtc, tzString: asString };
};
var parseStringDate = (stringDate) => {
  const parsed = stringDate.split(/[\s-:]/).map((i) => i * 1);
  parsed[1] -= 1;
  return parsed;
};
var min = -6e4;
var hour = 36e5;
var day = -864e5;
var month = 2592e6;
var findOffsetDates = (timeZone, year) => {
  const cached = offsetDateCache.get(timeZone, year);
  if (cached) {
    return cached;
  }
  const offsets = new TimeZoneOffsetInfo(timeZone, year), startUtcTicks = new Date(Date.UTC(year, 0, 1)).getTime();
  let ticks = startUtcTicks, incr = month, monthIndex = 0, previousOffset = [], currentOffset = null, tzTicksUtc, tzString, offset, done;
  while (!done) {
    ({ offset, tzTicksUtc, tzString } = getOffsetUtc(ticks, timeZone));
    if (currentOffset == null) {
      currentOffset = offset;
      offsets.push({ offset });
    } else if (incr > 0 && offset !== currentOffset) {
      incr = incr === month ? day : min;
    } else if (incr < 0 && offset === currentOffset) {
      if (incr === day) {
        incr = hour;
      } else {
        if (offsets.length === 1) {
          offsets.push(previousOffset);
          currentOffset = previousOffset.offset;
        } else {
          offsets[0].startTicks = previousOffset.startTicks;
          offsets[0].startDateString = previousOffset.startDateString;
          done = true;
        }
        incr = month;
      }
    }
    previousOffset = { offset, startDateString: tzString, startTicks: tzTicksUtc };
    if (incr === month) {
      ticks = startUtcTicks;
      ticks += monthIndex * month;
      monthIndex += 1;
    }
    ticks += incr;
    if (monthIndex > 11) {
      done = true;
    }
  }
  offsetDateCache.set(offsets);
  return offsets;
};
var offsetDateCache = {
  _cache: {},
  get(timeZone, year) {
    var _a5;
    return (_a5 = this._cache[timeZone]) == null ? void 0 : _a5[year];
  },
  set(offsetInfo) {
    const { timeZone } = offsetInfo, { _cache } = this;
    if (!_cache[timeZone]) {
      _cache[timeZone] = {};
    }
    _cache[timeZone][offsetInfo.year] = offsetInfo;
  }
};
var TimeZoneHelper = class {
  /**
   * Adjusts the time of the specified date to match the specified time zone. i.e. "what time is it now in this
   * timezone?"
   *
   * JavaScript dates are always in the local time zone. This function adjusts the time to match the time in the
   * specified time zone, without altering the time zone. Thus, it won't hold the same time as the original date.
   *
   * Note that this time zone calculation relies on the browsers built-in functionality to convert a local date to a
   * string in a given time zone and then converting the string back into a date. If browsers time zone information
   * or interpretation is inaccurate or lacks data, the conversion will probably be inaccurate as well.
   *
   * ```javascript
   * const localDate = new Date(2020, 7, 31, 7); // UTC+2 ('Europe/Stockholm')
   * const cstDate   = TimeZoneHelper.toTimezone(localDate, 'America/Chicago'); // 2020, 7, 31, 0 (still UTC+2, but
   * // appear as UTC-6)
   * ```
   *
   * @static
   * @param {Date} date
   * @param {String|Number} timeZone Timezone supported by `Intl.DateFormat` or a UTC offset in minutes
   * @returns {Date}
   */
  static toTimeZone(date2, timeZone) {
    if (typeof timeZone === "number") {
      return this.toUtcOffset(date2, timeZone);
    }
    const tzDate = toTimeZoneInternal(date2, timeZone);
    if (tzDate.asArray[3] !== tzDate.asLocalDate.getHours()) {
      console.warn("Incorrect time zone conversion due to local DST-switch detected");
    }
    return tzDate.asLocalDate;
  }
  /**
   * Adjusts the time of the specified date to match local system time zone in the specified time zone. i.e. "what
   * time in my timezone would match time in this timezone?"
   *
   * JavaScript dates are always in the local time zone. This function adjusts the time to match the time in the
   * specified time zone, without altering the time zone. Thus, it won't hold the same time as the original date.
   *
   * Note that this time zone calculation relies on the browsers built-in functionality to convert a date from a given
   * timezone into a local date by calculating specified time zone UTC offsets and using those to perform the date
   * conversion. If browsers time zone information or interpretation is inaccurate or lacks data, the conversion will
   * probably be inaccurate as well.
   *
   * ```javascript
   * const cstDate   = new Date(2022, 8, 27, 4); // CST 'America/Chicago'
   * const localDate = TimeZoneHelper.fromTimeZone(cstDate, 'America/Chicago'); // 2022, 8, 27, 11 (UTC+2 Europe/Stockholm)
   * ```
   *
   * @static
   * @param {Date} date
   * @param {String|Number} timeZone Timezone supported by Intl.DateFormat or a UTC offset in minutes
   * @returns {Date}
   */
  static fromTimeZone(date2, timeZone) {
    if (typeof timeZone === "number") {
      return this.fromUtcOffset(date2, timeZone);
    }
    const dateArr = this.dateAsArray(date2), offsetDates = findOffsetDates(timeZone, date2.getUTCFullYear());
    let useOffset = offsetDates[0].offset;
    if (offsetDates.length === 2) {
      const utcTicks = Date.UTC(...dateArr);
      if (utcTicks >= offsetDates[1].startTicks && utcTicks < offsetDates[0].startTicks) {
        useOffset = offsetDates[1].offset;
      }
    }
    dateArr[4] += useOffset;
    return new Date(Date.UTC(...dateArr));
  }
  /**
   * Adjusts the time of the specified date with provided UTC offset in minutes
   *
   * JavaScript dates are always in the local time zone. This function adjusts the time to match the time in the
   * specified time zone, without altering the time zone. Thus, it won't hold the same time as the original date.
   *
   * ```javascript
   * const localDate = new Date(2020, 7, 31, 7); // UTC+2
   * const utcDate   = TimeZoneHelper.toUtcOffset(localDate, 0); // 2020, 7, 31, 5 (still UTC+2, but appear as UTC+0)
   * ```
   *
   * @static
   * @private
   * @param {Date} date
   * @param {Number} utcOffset in minutes
   * @returns {Date}
   */
  static toUtcOffset(date2, utcOffset) {
    const offset = date2.getTimezoneOffset() + utcOffset;
    return new Date(date2.getTime() + offset * 60 * 1e3);
  }
  /**
   * Adjusts the time of the specified date by removing the provided UTC offset in minutes.
   *
   * JavaScript dates are always in the local time zone. This function adjusts the time to match the time in the
   * specified time zone, without altering the time zone. Thus, it won't hold the same time as the original date.
   *
   * ```javascript
   * const utcDate = new Date(2020, 7, 31, 7); // UTC
   * const utcDate = TimeZoneHelper.fromUtcOffset(localDate, 0); // 2020, 7, 31, 9 (matches 2020-08-31 07:00+00:00)
   * ```
   *
   * @static
   * @private
   * @param {Date} date
   * @param {Number} utcOffset in minutes
   * @returns {Date}
   */
  static fromUtcOffset(date2, utcOffset) {
    const offset = -date2.getTimezoneOffset() - utcOffset;
    return new Date(date2.getTime() + offset * 60 * 1e3);
  }
  // Converts a date into an array of its parts ([year, month, day, etc.]).
  // Convenient as a date info bearer which is not affected by local time zone
  static dateAsArray(date2) {
    return [date2.getFullYear(), date2.getMonth(), date2.getDate(), date2.getHours(), date2.getMinutes(), date2.getSeconds(), date2.getMilliseconds()];
  }
};
__publicField(TimeZoneHelper, "$name", "TimeZoneHelper");
TimeZoneHelper.findOffsetDates = findOffsetDates;
Helpers_default.register(TimeZoneHelper);
TimeZoneHelper._$name = "TimeZoneHelper";

// ../Core/lib/Core/widget/Toast.js
var _Toast = class _Toast extends Widget {
  static get configurable() {
    return {
      testConfig: {
        destroyTimeout: 1,
        timeout: 400
      },
      floating: true,
      /**
       * Timeout (in ms) until the toast is automatically dismissed. Set to 0 to never hide.
       * @config {Number}
       * @default
       */
      timeout: 2500,
      autoDestroy: null,
      // How long to wait after hide before destruction
      destroyTimeout: 200,
      /**
       * Show a progress bar indicating the time remaining until the toast is dismissed.
       * @config {Boolean}
       * @default
       */
      showProgress: true,
      /**
       * Toast color (should have match in toast.scss or your custom styling).
       * Valid values in Bryntum themes are:
       * * b-amber
       * * b-blue
       * * b-dark-gray
       * * b-deep-orange
       * * b-gray
       * * b-green
       * * b-indigo
       * * b-lime
       * * b-light-gray
       * * b-light-green
       * * b-orange
       * * b-purple
       * * b-red
       * * b-teal
       * * b-white
       * * b-yellow
       *
       * ```
       * new Toast({
       *    color : 'b-blue'
       * });
       * ```
       *
       * @config {String}
       */
      color: null,
      sideMargin: 20,
      /**
       * Which side to show the toast at, `'top'` or `'bottom'`. Defaults to `'bottom'`.
       *
       * May also define the inline edge to show at, by using `'top-start'`, or `'top-end'` etc.
       *
       * By default, toasts are shown at the bottom at the inline-end edge.
       * @config {'top'|'bottom'|'start'|'end'|'top-start'|'top-end'|'bottom-start'|'bottom-end'} side
       * @default
       */
      side: "bottom",
      role: "alert"
    };
  }
  changeSide(side) {
    if (side == "top" || side == "bottom") {
      side += "-end";
    } else if (side === "start" || side == "end") {
      side = "bottom-" + side;
    }
    return side;
  }
  compose() {
    const { appendTo, color, html, showProgress, style, timeout, side } = this;
    return {
      parent: appendTo || this.floatRoot,
      class: {
        ...DomClassList.normalize(color, "object"),
        [`b-side-${side}`]: 1,
        "b-toast-hide": 1
        // toasts start hidden so we can animate them into view
      },
      // Needed so that any element attribute modification doesn't restart the announcement
      "aria-atomic": false,
      html,
      style: {
        ...DomHelper.parseStyle(style),
        "--side": side.split("-")[0]
      },
      children: {
        progressElement: showProgress && {
          style: `animation-duration:${timeout}ms;`,
          class: {
            "b-toast-progress": 1
          }
        }
      },
      // eslint-disable-next-line bryntum/no-listeners-in-lib
      listeners: {
        click: "hide"
      }
    };
  }
  doDestroy() {
    this.untoast();
    super.doDestroy();
  }
  getNextInset(side) {
    const { sideMargin, element } = this;
    return parseInt(element.style[side], 10) + element.offsetHeight + sideMargin;
  }
  /**
   * Show the toast
   */
  async show() {
    var _a5, _b2, _c2;
    await super.show(...arguments);
    const me = this, { element, side } = me, toasts = _Toast.toasts[side], s = side.split("-")[0], timeout = me.timeout * (((_a5 = DomHelper.getActiveElement()) == null ? void 0 : _a5.matches(":focus-visible")) ? 3 : 1);
    if (!toasts.includes(me)) {
      element.style[s] = ((_c2 = (_b2 = toasts[0]) == null ? void 0 : _b2.getNextInset(s)) != null ? _c2 : me.sideMargin) + "px";
      toasts.unshift(me);
      element.getBoundingClientRect();
      element.classList.remove("b-toast-hide");
      if (timeout > 0) {
        me.hideTimeout = me.setTimeout("hide", timeout);
      }
    }
  }
  /**
   * Hide the toast
   */
  async hide() {
    const me = this;
    me.untoast();
    me.element.classList.add("b-toast-hide");
    if (me.autoDestroy && !me.destroyTimer) {
      me.destroyTimer = me.setTimeout("destroy", me.destroyTimeout);
    }
  }
  untoast() {
    const toasts = _Toast.toasts[this.side];
    if (toasts.includes(this)) {
      toasts.splice(toasts.indexOf(this), 1);
    }
    DomHelper.removeAttributeValue(document.body, "aria-describedby", this.id);
  }
  /**
   * Hide all visible toasts
   */
  static hideAll() {
    Object.keys(_Toast.toasts).forEach((k) => _Toast.toasts[k].slice().reverse().forEach((toast) => toast.hide()));
  }
  /**
   * Easiest way to show a toast
   *
   * ```javascript
   * Toast.show('Hi');
   *
   * Toast.show({
   *   html   : 'Read quickly, please',
   *   timeout: 1000
   * });
   * ```
   *
   * @param {String|ToastConfig} config Message or toast config object
   * @returns {Core.widget.Toast}
   */
  static show(config) {
    var _a5;
    const toast = _Toast.new({
      autoDestroy: true,
      rootElement: document.body,
      autoShow: false
    }, typeof config === "string" ? { html: config } : config);
    if (((_a5 = DomHelper.getActiveElement()) == null ? void 0 : _a5.matches(":focus-visible")) && Object.values(this.toasts).flat().length) {
      AsyncHelper.waitFor(() => !Object.values(this.toasts).flat().length).then(() => setTimeout(() => toast.show(), 250));
    } else {
      toast.show();
    }
    return toast;
  }
};
__publicField(_Toast, "$name", "Toast");
__publicField(_Toast, "type", "toast");
__publicField(_Toast, "toasts", {
  "top-start": [],
  "top-end": [],
  "bottom-start": [],
  "bottom-end": []
});
var Toast = _Toast;
Toast.initClass();
Toast._$name = "Toast";

// ../Core/lib/Core/helper/WidgetHelper.js
var WidgetHelper = class {
  //region Querying
  /**
   * Returns the widget with the specified id.
   * @param {String} id Id of widget to find
   * @returns {Core.widget.Widget} The widget if any
   * @category Querying
   */
  static getById(id2) {
    VersionHelper.deprecate("core", "7.0.0", "Use Widget.getById instead");
    return Widget.getById(id2);
  }
  /**
   * Returns the Widget which owns the passed element (or event).
   * @param {HTMLElement|Event} element The element or event to start from
   * @param {String|Function} [type] The type of Widget to scan upwards for. The lowercase
   * class name. Or a filter function which returns `true` for the required Widget.
   * @param {HTMLElement|Number} [limit] The number of components to traverse upwards to find a
   * match of the type parameter, or the element to stop at.
   * @returns {Core.widget.Widget} The found Widget or null.
   * @category Querying
   */
  static fromElement(element, type, limit) {
    VersionHelper.deprecate("core", "7.0.0", "Use Widget.fromElement instead");
    return Widget.fromElement(element, type, limit);
  }
  //endregion
  //region Widgets
  /**
   * Create a widget.
   *
   * ```javascript
   * WidgetHelper.createWidget({
   *   type: 'button',
   *   icon: 'user',
   *   text: 'Edit user'
   * });
   * ```
   *
   * @param {ContainerItemConfig} config Widget config
   * @returns {Core.widget.Widget} The widget
   * @category Widgets
   */
  static createWidget(config = {}) {
    VersionHelper.deprecate("core", "7.0.0", "Use Widget.create instead");
    return config.isWidget ? config : Widget.create(config);
  }
  /**
   * Appends a widget (array of widgets) to the DOM tree. If config is empty, widgets are appended to the DOM. To
   * append widget to certain position you can pass HTMLElement or its id as config, or as a config, that will be
   * applied to all passed widgets.
   *
   * Usage:
   *
   * ```javascript
   * // Will append button as last item to element with id 'container'
   * let [button] = WidgetHelper.append({ type : 'button' }, 'container');
   *
   * // Same as above, but will add two buttons
   * let [button1, button2] = WidgetHelper.append([
   *     { type : 'button' },
   *     { type : 'button' }
   *     ], { appendTo : 'container' });
   *
   * // Will append two buttons before element with id 'someElement'. Order will be preserved and all widgets will have
   * // additional class 'my-cls'
   * let [button1, button2] = WidgetHelper.append([
   *     { type : 'button' },
   *     { type : 'button' }
   *     ], {
   *         insertBefore : 'someElement',
   *         cls          : 'my-cls'
   *     });
   * ```
   *
   * @param {ContainerItemConfig|ContainerItemConfig[]} widget Widget config or array of such configs
   * @param {HTMLElement|String|Object} [config] Element (or element id) to which to append the widget or config to
   * apply to all passed widgets
   * @returns {Core.widget.Widget[]} Array or widgets
   * @category Widgets
   */
  static append(widget, config) {
    VersionHelper.deprecate("core", "7.0.0", "Use a Container instead");
    widget = Array.isArray(widget) && widget || [widget];
    if (config instanceof HTMLElement || typeof config === "string") {
      config = {
        appendTo: config
      };
    }
    if (config.insertFirst) {
      const target = typeof config.insertFirst === "string" ? document.getElementById(config.insertFirst) : config.insertFirst;
      if (target.firstChild) {
        config.insertBefore = target.firstChild;
      } else {
        config.appendTo = target;
      }
    }
    return widget.map((item) => Widget.create(ObjectHelper.assign({}, config || {}, item)));
  }
  //endregion
  //region Popups
  /**
   * Shows a popup (~tooltip) containing widgets connected to specified element.
   *
   * ```javascript
   * WidgetHelper.openPopup(element, {
   *   position: 'bottom center',
   *   items: [
   *      { widgetConfig }
   *   ]
   * });
   * ```
   *
   * @param {HTMLElement} element Element to connect popup to
   * @param {PopupConfig} config Config object, or string to use as html in popup
   * @returns {*|{close, widgets}}
   * @category Popups
   */
  static openPopup(element, config) {
    VersionHelper.deprecate("core", "7.0.0", "Use Popup directly instead");
    return Widget.create(ObjectHelper.assign({
      forElement: element
    }, typeof config === "string" ? {
      html: config
    } : config), "popup");
  }
  /**
   * Shows a context menu connected to the specified element.
   *
   * ```javascript
   * WidgetHelper.showContextMenu(element, {
   *   items: [
   *      { id: 'addItem', icon: 'add', text: 'Add' },
   *      ...
   *   ],
   *   onItem: item => alert('Clicked ' + item.text)
   * });
   * ```
   *
   * @param {HTMLElement|Number[]} element Element (or a coordinate) to show the context menu for
   * @param {MenuItemConfig} config Context menu config, see example
   * @returns {Core.widget.Menu}
   * @category Popups
   */
  static showContextMenu(element, config) {
    VersionHelper.deprecate("core", "7.0.0", "Use Menu directly instead");
    const me = this;
    if (me.currentContextMenu) {
      me.currentContextMenu.destroy();
    }
    if (element instanceof HTMLElement) {
      config.forElement = element;
    } else {
      config.forElement = document.body;
      if (Array.isArray(element)) {
        element = new Point(...element);
      }
      if (element instanceof Point) {
        config.align = {
          position: element
        };
      }
    }
    config.internalListeners = { destroy: me.currentContextMenu = null };
    return me.currentContextMenu = Widget.create(config, "menu");
  }
  /**
   * Attached a tooltip to the specified element.
   *
   * ```javascript
   * WidgetHelper.attachTooltip(element, {
   *   text: 'Useful information goes here'
   * });
   * ```
   *
   * @param {HTMLElement} element Element to attach tooltip for
   * @param {String|TooltipConfig} configOrText Tooltip config or tooltip string, see example and source
   * @returns {HTMLElement} The passed element
   * @category Popups
   */
  static attachTooltip(element, configOrText) {
    VersionHelper.deprecate("core", "7.0.0", "Use Tooltip directly instead");
    return Widget.attachTooltip(element, configOrText);
  }
  /**
   * Checks if element has tooltip attached
   *
   * @param {HTMLElement} element Element to check
   * @returns {Boolean}
   * @category Popups
   */
  static hasTooltipAttached(element) {
    VersionHelper.deprecate("core", "7.0.0", "Will be removed");
    return Widget.resolveType("tooltip").hasTooltipAttached(element);
  }
  /**
   * Destroys any tooltip attached to an element, removes it from the DOM and unregisters any tip related listeners
   * on the element.
   *
   * @param {HTMLElement} element Element to remove tooltip from
   * @category Popups
   */
  static destroyTooltipAttached(element) {
    VersionHelper.deprecate("core", "7.0.0", "Will be removed");
    return Widget.resolveType("tooltip").destroyTooltipAttached(element);
  }
  //endregion
  //region Mask
  /**
   * Masks the specified element, showing a message in the mask.
   * @param {HTMLElement} element Element to mask
   * @param {String} msg Message to show in the mask
   * @returns {Core.widget.Mask}
   * @category Mask
   */
  static mask(element, msg = "Loading") {
    VersionHelper.deprecate("core", "7.0.0", "Use Mask directly instead");
    if (element) {
      if (element instanceof HTMLElement) {
        element = {
          target: element,
          text: msg
        };
      }
      return Mask.mask(element, element.target);
    }
  }
  /**
   * Unmask the specified element.
   * @param {HTMLElement} element
   * @category Mask
   */
  static unmask(element, close = true) {
    VersionHelper.deprecate("core", "7.0.0", "Use Mask directly instead");
    if (element.mask) {
      if (close) {
        element.mask.close();
      } else {
        element.mask.hide();
      }
    }
  }
  //endregion
  //region Toast
  /**
   * Show a toast
   * @param {String} msg message to show in the toast
   * @category Mask
   */
  static toast(msg) {
    VersionHelper.deprecate("core", "7.0.0", "Use Toast directly instead");
    return Toast.show(msg);
  }
  //endregion
};
WidgetHelper._$name = "WidgetHelper";

// ../Core/lib/Core/helper/XMLHelper.js
var XMLHelper = class {
  /**
   * Convert a JavaScript object to an XML string.
   *
   * From:
   * ```javascript
   * {
   *     name : 'Task 1',
   *     data : [
   *         {
   *             text : 'foo 1',
   *             ref  : 'fooItem 1'
   *         },
   *         {
   *             text : 'foo 2',
   *             ref  : 'fooItem 2'
   *         }
   *     ]
   * }
   * ```
   *
   * To:
   * ```xml
   * <?xml version="1.0" encoding="UTF-8" standalone="yes"?>
   * <root>
   *     <name>Task 1</name>
   *     <data>
   *         <element>
   *             <text>foo 1</text>
   *             <ref>fooItem 1</ref>
   *         </element>
   *         <element>
   *             <text>foo 2</text>
   *             <ref>fooItem 2</ref>
   *         </element>
   *     </data>
   * </root>
   * ```
   *
   * @param {Object} obj Object to convert.
   * @param {Object} [options] Convert options.
   * @param {String} [options.rootName] Root name for the XML. `root` by default.
   * @param {String} [options.elementName] Element name for each node of the XML. `element` by default.
   * @param {String} [options.xmlns] Add value for xmlns property for the root tag of the XML.
   * @param {Boolean} [options.includeHeader] `false` to not include the header `<?xml version="1.0" encoding="UTF-8"?>` on top of the XML.
   * @param {Boolean} [options.rootElementForArray] `false` to not include a root element for array of items. e.g. for the above example:
   * ```xml
   * <?xml version="1.0" encoding="UTF-8" standalone="yes"?>
   * <root>
   *     <name>Task 1</name>
   *     <element>
   *         <text>foo 1</text>
   *         <ref>fooItem 1</ref>
   *     </element>
   *     <element>
   *         <text>foo 2</text>
   *         <ref>fooItem 2</ref>
   *     </element>
   * </root>
   * ```
   * @returns {String} the XML
   */
  static convertFromObject(obj, options = {}) {
    Objects.assignIf(options, {
      rootName: "root",
      elementName: "element",
      includeHeader: true,
      rootElementForArray: true
    });
    const { rootName, elementName, includeHeader, rootElementForArray } = options;
    let { xmlns } = options;
    xmlns = xmlns ? ` xmlns="${xmlns}"` : "";
    const header = includeHeader ? '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' : "";
    const converter = (o) => {
      const xmlItems = [];
      for (const key in o) {
        const item = o[key];
        if (Array.isArray(item)) {
          if (rootElementForArray) {
            xmlItems.push(`<${key}>`);
          }
          for (const subItem of item) {
            if (elementName.length) {
              xmlItems.push(`<${elementName}>`);
            } else {
              xmlItems.push(`<${key}>`);
            }
            xmlItems.push(converter(subItem));
            if (elementName.length) {
              xmlItems.push(`</${elementName}>`);
            } else {
              xmlItems.push(`</${key}>`);
            }
          }
          if (rootElementForArray) {
            xmlItems.push(`</${key}>`);
          }
        } else if (Objects.isObject(item)) {
          xmlItems.push(`<${key}>${converter(item)}</${key}>`);
        } else {
          if (item == null) {
            xmlItems.push(`<${key}/>`);
          } else {
            xmlItems.push(`<${key}>${StringHelper.encodeHtml(item)}</${key}>`);
          }
        }
      }
      return xmlItems.join("");
    };
    return `${header}<${rootName}${xmlns}>${converter(obj)}</${rootName}>`;
  }
};
XMLHelper._$name = "XMLHelper";

// ../Core/lib/Core/helper/util/RandomGenerator.js
var RandomGenerator = class {
  constructor() {
    this.random100 = [46, 2, 36, 46, 54, 59, 18, 20, 71, 55, 88, 98, 13, 61, 61, 40, 2, 15, 3, 32, 51, 45, 64, 25, 81, 85, 54, 13, 57, 49, 64, 22, 81, 94, 0, 62, 17, 7, 11, 2, 33, 99, 85, 26, 83, 83, 96, 26, 20, 89, 91, 38, 26, 13, 11, 79, 32, 30, 5, 51, 70, 7, 5, 56, 58, 77, 37, 89, 40, 80, 78, 59, 26, 36, 8, 51, 60, 23, 86, 5, 11, 96, 64, 94, 87, 64, 4, 78, 17, 85, 35, 0, 90, 86, 23, 55, 53, 9, 35, 59, 29, 2, 64, 42, 8, 49, 43, 73, 6, 53, 38, 9, 39, 31, 32, 40, 49, 13, 78, 68, 20, 99, 24, 78, 35, 91, 73, 46, 67, 76, 89, 69, 30, 69, 25, 3, 4, 55, 1, 65, 66, 76, 83, 19, 67, 1, 95, 24, 54, 45, 56, 40, 67, 92, 72, 4, 69, 8, 47, 50, 27, 2, 38, 9, 14, 83, 12, 14, 62, 95, 22, 47, 35, 18, 38, 14, 86, 64, 68, 61, 52, 69, 39, 93, 20, 73, 32, 52, 74, 6, 56, 68, 99, 29, 24, 92, 40, 67, 6, 72, 31, 41, 91, 53, 80, 55, 33, 97, 97, 99, 18, 20, 5, 27, 82, 84, 61, 78, 27, 67, 7, 42, 75, 95, 91, 25, 63, 21, 70, 36, 46, 0, 1, 45, 84, 6, 86, 15, 10, 62, 96, 94, 10, 23, 93, 83, 94, 47, 5, 29, 29, 52, 51, 37, 77, 96, 43, 72, 43, 14, 54, 14, 72, 52, 4, 39, 15, 26, 68, 28, 25, 76, 60, 50, 22, 40, 72, 74, 68, 58, 8, 48, 40, 62, 52, 24, 9, 26, 47, 44, 49, 96, 7, 77, 90, 45, 76, 47, 5, 86, 1, 36, 18, 42, 19, 90, 34, 23, 70, 32, 69, 79, 0, 99, 57, 80, 72, 21, 19, 72, 85, 68, 4, 40, 86, 62, 0, 63, 4, 11, 69, 31, 78, 31, 21, 78, 29, 84, 13, 53, 57, 10, 26, 50, 24, 30, 90, 42, 51, 96, 93, 21, 99, 23, 81, 0, 89, 43, 86, 63, 93, 19, 54, 71, 92, 36, 4, 95, 37, 99, 60, 29, 23, 50, 68, 95, 57, 95, 77, 53, 99, 78, 75, 12, 92, 47, 23, 14, 0, 41, 98, 11, 34, 64, 26, 90, 50, 23, 38, 31, 74, 76, 16, 76, 66, 23, 22, 72, 48, 50, 20, 36, 37, 58, 5, 43, 49, 64, 81, 30, 8, 21, 98, 75, 60, 17, 50, 42, 27, 38, 90, 74, 45, 68, 67, 27, 31, 15, 58, 76, 41, 99, 23, 98, 53, 98, 56, 19, 79, 2, 4, 38, 96, 24, 65, 51, 43, 42, 41, 60, 46, 7, 90, 65, 3, 27, 63, 99, 51, 44, 86, 1, 54, 40, 15, 74, 3, 81, 51, 63, 87, 79, 84, 72, 22, 38, 96, 95, 33, 41, 21, 99, 21, 69, 7, 49, 40, 52, 41, 6, 91, 19, 76, 40, 54, 17, 33, 11, 11, 0, 1, 32, 94, 33, 13, 18, 45, 7, 85, 61, 42, 54, 45, 72, 78, 96, 17, 9, 80, 87, 41, 96, 66, 0, 8, 59, 18, 21, 2, 28, 64, 75, 97, 32, 80, 86, 97, 97, 55, 2, 73, 75, 11, 89, 67, 58, 70, 76, 12, 46, 64, 17, 22, 97, 25, 35, 93, 57, 82, 46, 57, 61, 31, 74, 27, 4, 32, 85, 53, 86, 53, 53, 42, 5, 28, 50, 65, 63, 70, 61, 73, 37, 13, 80, 7, 34, 22, 3, 26, 6, 62, 78, 12, 56, 87, 41, 58, 64, 31, 27, 45, 35, 18, 66, 62, 43, 89, 69, 94, 93, 33, 74, 2, 43, 85, 37, 82, 41, 74, 9, 15, 44, 33, 42, 65, 19, 1, 49, 78, 12, 29, 9, 78, 7, 55, 12, 45, 40, 33, 16, 86, 14, 52, 16, 73, 76, 0, 98, 75, 91, 78, 46, 99, 95, 90, 69, 78, 45, 62, 55, 37, 88, 49, 77, 27, 83, 38, 73, 39, 1, 75, 40, 65, 83, 54, 95, 7, 73, 4, 30, 26, 36, 89, 21, 5, 95, 11, 14, 87, 45, 36, 21, 77, 55, 5, 66, 51, 98, 48, 62, 74, 58, 23, 82, 30, 28, 19, 53, 89, 76, 98, 8, 34, 70, 28, 54, 16, 52, 35, 93, 54, 54, 72, 49, 18, 93, 72, 90, 71, 73, 15, 60, 38, 80, 76, 53, 70, 39, 69, 25, 5, 31, 61, 46, 6, 54, 34, 31, 52, 33, 36, 79, 76, 44, 29, 28, 38, 1, 66, 2, 90, 91, 1, 76, 78, 31, 55, 37, 71, 2, 3, 38, 85, 0, 95, 42, 2, 39, 57, 87, 61, 77, 98, 2, 24, 80, 48, 27, 47, 71, 15, 7, 49, 60, 86, 3, 2, 29, 38, 54, 36, 59, 83, 27, 47, 9, 36, 42, 8, 73, 85, 9, 16, 73, 60, 39, 12, 43, 25, 23, 29, 28, 47, 40, 77, 20, 89, 22, 30, 41, 59, 96, 19, 56, 20, 76, 73, 39, 46, 72, 40, 47, 37, 52, 29, 79, 37, 39, 50, 41, 87, 66, 17, 75, 31, 45, 26, 88, 70, 11, 90, 40, 74, 9, 32, 65, 72, 61, 6, 93, 54, 15, 84, 22, 99, 47, 10, 96, 4, 84, 19, 85, 73, 45, 25, 16, 8, 94, 99, 39, 28, 26, 68, 87, 48, 1, 65, 86, 46, 86, 7, 60, 82, 45, 75, 38, 56, 41, 35, 30, 86, 91, 97, 85, 45, 5, 14, 69, 85, 96, 37, 18, 26, 16, 38, 16, 1, 44, 94, 85, 58, 60, 20, 5, 47, 52, 41, 50, 71, 43, 42, 67, 64, 38, 65, 83, 99, 78, 96, 33, 20, 98, 24, 6, 2, 25, 16, 16, 44, 63, 24, 68, 56, 49, 91, 15, 59, 99, 27, 43, 34, 28, 36, 45, 1, 10, 19, 54, 26, 75, 17, 88, 96, 63, 24, 71, 93, 72, 97, 66, 87, 18, 86];
    this.randomCache = {};
    this.rndIndex = 0;
  }
  /**
   * Returns next pseudo random integer number from sequence between 0 and max parameter value (99 is maximum value)
   * ```javascript
   * const rand = new RandomGenerator();
   * const randomNumber = rand.nextRandom(50);
   * ```
   *
   * @param {Number} max max value
   * @returns {Number}
   */
  nextRandom(max3) {
    const { randomCache } = this;
    let randomNumbers;
    if (randomCache[max3]) {
      randomNumbers = randomCache[max3];
    } else {
      randomNumbers = this.random100.filter((num) => num < max3);
      randomCache[max3] = randomNumbers;
    }
    return randomNumbers[this.rndIndex++ % randomNumbers.length];
  }
  /**
   * Resets sequence to initial number
   */
  reset() {
    this.rndIndex = 0;
  }
  /**
   * Returns pseudo random array element
   * ```javascript
   * const rand = new RandomGenerator();
   * const cities = ['New York', 'London', 'Tokyo', 'Paris' ];
   * const randomCity = rand.fromArray(cities);
   * ```
   *
   * @param {Array} array Input array
   * @returns {*} A pseudo random element from the array
   */
  fromArray(array) {
    return array[this.nextRandom(array.length)];
  }
  /**
   * Creates a random array from a larger array of possibilities.
   * ```javascript
   * const rand = new RandomGenerator();
   * const cities = [
   * 'New York', 'London', 'Tokyo', 'Paris', 'Los Angeles',
   * 'Berlin', 'Sydney', 'Singapore', 'Rome', 'Toronto',
   * 'Hong Kong', 'Amsterdam', 'Barcelona', 'Dubai', 'Chicago',
   * 'Mumbai', 'Bangkok', 'Moscow', 'San Francisco', 'Madrid' ];
   * const randomCities = rand.randomArray(cities, 5);
   * ```
   *
   * @param {Array} array Input array
   * @param {Number} max Max length
   * @returns {Array}
   */
  randomArray(array, maxLength) {
    const result = [], length = this.nextRandom(maxLength + 1), used = {};
    for (let i = 0, index = this.nextRandom(array.length); i < length; i++) {
      while (used[index]) {
        index = this.nextRandom(array.length);
      }
      used[index] = true;
      result.push(array[index]);
    }
    return result;
  }
};
RandomGenerator._$name = "RandomGenerator";

// ../Core/lib/Core/helper/util/DataGenerator.js
var lorem = [
  "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
  "Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
  "Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.",
  "Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.",
  "Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo.",
  "Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui atione voluptatem sequi nesciunt.",
  "Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit, sed quia non numquam eius modi tempora incidunt ut labore et dolore magnam aliquam quaerat voluptatem.",
  "Ut enim ad minima veniam, quis nostrum exercitationem ullam corporis suscipit laboriosam, nisi ut aliquid ex ea commodi consequatur?",
  "Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur, vel illum qui dolorem eum fugiat quo voluptas nulla pariatur?"
];
var _DataGenerator = class _DataGenerator {
  //region Random
  static reset() {
    this.rnd.reset();
    this.rndTime.reset();
    this.rndRating.reset();
  }
  //region Data arrays
  /**
   * Array with available city values
   * @static
   * @readonly
   * @member {String[]}
   */
  static get cities() {
    return [
      "Stockholm",
      "Barcelona",
      "Paris",
      "Dubai",
      "New York",
      "San Francisco",
      "Washington",
      "Montreal"
    ];
  }
  static get namesWithAvatars() {
    return [
      "Adam",
      "Amit",
      "Angelo",
      "Arcady",
      "Arnold",
      "Barbara",
      "Celia",
      "Chang",
      "Dan",
      "Daniel",
      "Dave",
      "David",
      "Don",
      "Doug",
      "Emilia",
      "George",
      "Gloria",
      "Henrik",
      "Hitomi",
      "James",
      "Jane",
      "Jenny",
      "John",
      "Jong",
      "Karen",
      "Kate",
      "Lee",
      "Linda",
      "Lisa",
      "Lola",
      "Macy",
      "Madison",
      "Malik",
      "Mark",
      "Mary",
      "Maxim",
      "Melissa",
      "Mike",
      "Rob",
      "Steve",
      "Team",
      "Theo"
    ];
  }
  /**
   * Array with available first name values
   * @static
   * @readonly
   * @member {String[]}
   */
  static get firstNames() {
    return [
      "Mike",
      "Linda",
      "Don",
      "Karen",
      "Doug",
      "Jenny",
      "Daniel",
      "Melissa",
      "John",
      "Jane",
      "Theo",
      "Lisa",
      "Adam",
      "Mary",
      "Barbara",
      "James",
      "David"
    ];
  }
  /**
   * Array with available surname values
   * @static
   * @readonly
   * @member {String[]}
   */
  static get surNames() {
    return [
      "McGregor",
      "Ewans",
      "Scott",
      "Smith",
      "Johnson",
      "Adams",
      "Williams",
      "Brown",
      "Jones",
      "Miller",
      "Davis",
      "More",
      "Wilson",
      "Taylor",
      "Anderson",
      "Thomas",
      "Jackson"
    ];
  }
  /**
   * Array with available team values
   * @static
   * @readonly
   * @member {String[]}
   */
  static get teams() {
    return [
      "Lions",
      "Eagles",
      "Tigers",
      "Horses",
      "Dogs",
      "Cats",
      "Panthers",
      "Rats",
      "Ducks",
      "Cougars",
      "Hens",
      "Roosters"
    ];
  }
  /**
   * Array with available food values
   * @static
   * @readonly
   * @member {String[]}
   */
  static get foods() {
    return [
      "Pancake",
      "Burger",
      "Fish n chips",
      "Carbonara",
      "Taco",
      "Salad",
      "Bolognese",
      "Mac n cheese",
      "Waffles"
    ];
  }
  /**
   * Array with available color values
   * @static
   * @readonly
   * @member {String[]}
   */
  static get colors() {
    return [
      "Blue",
      "Green",
      "Red",
      "Yellow",
      "Pink",
      "Purple",
      "Orange",
      "Teal",
      "Black"
    ];
  }
  /**
   * Array with available skill values
   * @static
   * @readonly
   * @member {String[]}
   */
  static get skills() {
    return [
      "JavaScript",
      "CSS",
      "TypeScript",
      "React",
      "Vue",
      "Angular",
      "Java",
      "PHP",
      "Python",
      "C#",
      "C++",
      "BASIC",
      "COBOL",
      "FORTRAN",
      "PASCAL",
      "SQL"
    ];
  }
  /**
   * Array with available task values
   * @static
   * @readonly
   * @member {String[]}
   */
  static get tasks() {
    return [
      "Meetings",
      "Documentation",
      "Email communication",
      "Project management",
      "Budgeting",
      "Marketing and advertising",
      "Customer service",
      "Research and analysis",
      "Data entry",
      "IT support",
      "Employee management",
      "Sales and business development",
      "Event planning",
      "Graphic design",
      "Writing and editing",
      "Presentation",
      "Travel arrangements and expense management",
      "Training and development",
      "Quality assurance",
      "Customer support",
      "Technical writing",
      "Social media management",
      "Translation",
      "Legal research",
      "Data analysis and visualization",
      "Video editing and production",
      "Network admin",
      "Content creation",
      "Market research",
      "Public relations",
      "Teaching and training",
      "Recruiting",
      "Product development"
    ];
  }
  //endregion
  //region Generate data
  static generateName() {
    const {
      namesWithAvatars,
      surNames
    } = this, firstName = namesWithAvatars[Math.round(Math.random() * (namesWithAvatars.length - 1))], surName = surNames[Math.round(Math.random() * (surNames.length - 1))];
    return `${firstName} ${surName}`;
  }
  /**
   * Generates an array of objects with specified properties.
   *
   * @param {Number} count The number of objects to generate or an options object.
   * @privateparam {Boolean|Number} [randomHeight=false] If `true`, assigns random heights to objects. If a number is provided, it sets the maximum height.
   * @privateparam {Number} [initialId=1] The initial ID for the generated objects.
   * @yields {Object} An object representing a generated item with various properties.
   * @generator
   * @static
   */
  static *generate(count, randomHeight = false, initialId = 1) {
    var _a5;
    let addSkills, rowCallback;
    if (typeof count === "object") {
      randomHeight = count.randomHeight;
      initialId = (_a5 = count.initialId) != null ? _a5 : 1;
      addSkills = count.addSkills;
      rowCallback = count.rowCallback;
      count = count.count;
    }
    const me = this, {
      rnd,
      rndTime,
      rndRating,
      rndText,
      firstNames,
      surNames,
      teams,
      foods,
      colors,
      cities,
      skills
    } = me;
    for (let i = 0; i < count; i++) {
      const firstName = rnd.fromArray(firstNames), surName = rnd.fromArray(surNames), name = `${firstName} ${String.fromCharCode(65 + i % 25)} ${surName}`, startDay = rnd.nextRandom(60) + 1, start = new Date(2019, 0, startDay), finish = new Date(2019, 0, startDay + rnd.nextRandom(30) + 2), row = {
        id: initialId > -1 ? i + initialId : void 0,
        title: "Row " + i,
        name,
        firstName,
        surName,
        city: rnd.fromArray(cities),
        team: rnd.fromArray(cities) + " " + rnd.fromArray(teams),
        age: 10 + rnd.nextRandom(80),
        food: rnd.fromArray(foods),
        color: rnd.fromArray(colors),
        score: rnd.nextRandom(100) * 10,
        rank: rnd.nextRandom(100) + 1,
        start,
        finish,
        time: DateHelper.getTime(rndTime.nextRandom(24), rndTime.nextRandom(12) * 5),
        percent: rnd.nextRandom(100),
        done: rnd.nextRandom(100) < 50,
        rating: rndRating.nextRandom(5),
        active: startDay > 30,
        relatedTo: Math.min(count - 1, i + initialId + rnd.nextRandom(10)),
        notes: lorem[rndText.nextRandom(7) + 1]
      };
      if (addSkills) {
        row.skills = rnd.randomArray(skills, typeof addSkills === "number" ? addSkills : 7);
      }
      const additionalData = rowCallback == null ? void 0 : rowCallback(row);
      additionalData && Object.assign(row, additionalData);
      if (randomHeight) {
        row.rowHeight = rnd.nextRandom(randomHeight === true ? 20 : randomHeight) * 5 + 20;
      }
      yield row;
    }
  }
  /**
   * Generates a pseudo random dataset. Used in Grid examples.
   * @param {Number|Object} count number of records, or an object with properties:
   * @param {Boolean} [count.randomHeight] Generate random row height
   * @param {Boolean} [count.initialId] Row initial id. Set -1 to disable Id generation. Defaults to 1.
   * @param {Boolean} [count.reset] Set true to ensure we get the same dataset on consecutive calls. Defaults to true
   * @param {Boolean} [count.rowCallback] A callback called for each row to allow appending extra data, returned data in
   * object form will be applied to the generated data
   * @param {Boolean} [count.addSkills] Add skills to the dataset
   * @param {Boolean} [randomHeight] Generate random row height
   * @param {Number} [initialId] Row initial id. Set -1 to disable Id generation. Defaults to 1.
   * @param {Boolean} [reset] Set true to ensure we get the same dataset on consecutive calls. Defaults to true
   * @returns {Object[]} Generated rows array
   */
  static generateData(count, randomHeight = false, initialId = 1, reset = true) {
    let args = count;
    if (typeof count !== "object") {
      args = {
        count,
        randomHeight,
        initialId,
        reset
      };
    }
    args.reset !== false && this.reset();
    if (_DataGenerator.overrideRowCount) {
      args.count = _DataGenerator.overrideRowCount;
    }
    const rows = [], generator = this.generate(args);
    for (let i = 0; i < args.count; i++) {
      rows.push(generator.next().value);
    }
    return rows;
  }
  /**
   * Generates a dataset of events
   * @returns {Object[]}
   */
  static generateEvents({
    viewStartDate,
    viewEndDate,
    nbrResources = 50,
    nbrEvents = 5,
    dependencies,
    tickUnit = "days",
    minDuration = 2,
    maxDuration = 10,
    gap = 1,
    eventCallback
  }) {
    const resources = this.generateData(nbrResources), events = [];
    let startDate, endDate;
    dependencies = [];
    for (let i = 0; i <= nbrResources; i++) {
      for (let j = 0; j <= nbrEvents; j++) {
        const duration = Math.round(Math.random() * (maxDuration - 1 - minDuration)) + minDuration, eventId = events.length + 1;
        startDate = j === 0 ? DateHelper.add(viewStartDate, Math.round(Math.random() * 3) * gap, tickUnit) : DateHelper.add(endDate, gap, tickUnit);
        endDate = DateHelper.add(startDate, duration, tickUnit);
        events.push({
          id: eventId,
          name: this.tasks[(i + j) % (this.tasks.length - 1)],
          startDate,
          duration,
          endDate,
          resourceId: i
        });
        eventCallback == null ? void 0 : eventCallback(events[events.length - 1]);
        if (dependencies && i > 0) {
          dependencies.push({
            id: dependencies.length + 1,
            from: eventId - 1,
            to: eventId
          });
        }
      }
    }
    return {
      resources,
      events,
      dependencies
    };
  }
  /**
   * Generates a pseudo random dataset with one scheduled event per resource & date tick.
   * @param {Object} data
   * @param {Boolean} [data.startDate] Start date of the first generated event
   * @param {Boolean} [data.endDate] End date of the last generated event
   * @param {Boolean} [data.nbrResources] Number of resources
   * @param {Boolean} [data.tickUnit] The tick unit for the time axis, defaults to `days`
   * @returns {Object[]} Generated rows array
   */
  static generateOneEventPerTickAndResource({
    startDate,
    endDate,
    nbrResources = 50,
    tickUnit = "days"
  }) {
    const resources = this.generateData(nbrResources), events = [], visibleTicks = DateHelper.getDurationInUnit(startDate, endDate, tickUnit);
    for (let i = 0; i <= nbrResources; i++) {
      for (let j = 0; j < visibleTicks; j++) {
        events.push({
          id: events.length + 1,
          resourceId: i,
          name: this.tasks[(i + j) % (this.tasks.length - 1)],
          startDate: DateHelper.add(startDate, j, tickUnit),
          duration: 1,
          durationUnit: tickUnit
        });
      }
    }
    return {
      resources,
      events
    };
  }
  /**
   * Generates pseudo random data for a Grid row.
   * @returns {Object} Generated row
   */
  static generateRow() {
    return _DataGenerator.generateData(1, false, -1, false)[0];
  }
  //endregion
};
//endregion
__publicField(_DataGenerator, "rnd", new RandomGenerator());
__publicField(_DataGenerator, "rndTime", new RandomGenerator());
__publicField(_DataGenerator, "rndRating", new RandomGenerator());
__publicField(_DataGenerator, "rndText", new RandomGenerator());
var DataGenerator = _DataGenerator;
DataGenerator._$name = "DataGenerator";

// ../Core/lib/Core/helper/util/DemoBot.js
var knownProps = [
  "action",
  "target",
  "to",
  "deltaX",
  "deltaY",
  "x",
  "y",
  "text"
];
var DemoBot = class extends Events_default(Delayable_default()) {
  static get defaultConfig() {
    return {
      repeat: true,
      outerElement: document.body,
      callOnFunctions: true
    };
  }
  // expects an outer element (grid.element/scheduler.element) and an array of steps similar to chain steps in siesta
  construct(config) {
    super.construct(config);
    const me = this;
    if (me.widget) {
      me.outerElement = me.widget.element;
      me.widget.playingDemo = true;
    }
    EventHelper.playingDemo = true;
    Object.assign(me, {
      prevTarget: null,
      currentStep: 0,
      mouse: DomHelper.createElement({
        parent: me.outerElement,
        tag: "div",
        className: "simulated-mouse"
      }),
      timeoutId: null,
      innerIntervalId: null,
      mouseOutElements: []
    });
    me.intervalId = me.setInterval(me.nextStep.bind(me), 1e3);
    me.outerElement.classList.add("b-playing-demo");
    me.outerElement.addEventListener("click", (event) => {
      if (event.isTrusted) {
        me.abort();
      }
    });
  }
  doDestroy() {
    this.abort();
  }
  // stops the bot
  abort(atEnd = false) {
    const me = this;
    me.mouse.style.top = "-100px";
    me.clearInterval(me.intervalId);
    me.timeoutId && me.clearTimeout(me.timeoutId);
    me.innerIntervalId && me.clearInterval(me.innerIntervalId);
    me.outerElement.classList.remove("b-playing-demo");
    if (me.widget) {
      me.widget.playingDemo = false;
    }
    EventHelper.playingDemo = false;
    me.trigger(atEnd ? "done" : "abort");
  }
  // triggers a synthetic event
  triggerEvent(element, type, data) {
    if (!element)
      return null;
    let event;
    if (type.startsWith("mouse")) {
      const box = this.mouse.getBoundingClientRect();
      event = new MouseEvent(type, Object.assign({
        view: globalThis,
        bubbles: true,
        cancelable: true,
        clientX: box.left,
        clientY: box.top
      }, data || {}));
    } else {
      event = document.createEvent("Event");
      event.initEvent(type, true, false);
    }
    element.dispatchEvent(event);
    return event;
  }
  // moves mouse to target in 10 steps, with animated transition between steps
  handleMouseMove(step, target) {
    const me = this, mouse = me.mouse;
    mouse.classList.add("quick");
    if (me.mouseDown)
      mouse.classList.add("drag");
    const mouseBox = Rectangle.from(mouse, me.outerElement), x = mouseBox.x, y = mouseBox.y;
    let deltaX = 0, deltaY = 0;
    if (step.to) {
      if (typeof step.to === "string") {
        const toElement = me.outerElement.querySelector(step.to);
        if (toElement) {
          const rect = Rectangle.from(toElement, me.outerElement), toX = rect.x + rect.width / 2, toY = rect.y + rect.height / 2;
          deltaX = (toX - x) / 10;
          deltaY = (toY - y) / 10;
        }
      } else if (step.to.x) {
        deltaX = (step.to.x - x) / 10;
      } else {
        deltaX = step.to[0] / 10;
        deltaY = step.to[1] / 10;
      }
    } else if (step.deltaX) {
      deltaX = step.deltaX / 10;
    } else if (step.x) {
      deltaX = (step.x - x) / 10;
    }
    if (step.deltaY) {
      deltaY = step.deltaY / 10;
    }
    let i = 0;
    me.innerIntervalId = me.setInterval(() => {
      if (me.shouldPause) {
        return;
      }
      if (i++ === 9) {
        clearInterval(me.innerIntervalId);
        if (step.then) {
          step.then();
        }
      }
      const mouseX = x + deltaX * i, mouseY = y + deltaY * i;
      mouse.style.left = mouseX + "px";
      mouse.style.top = mouseY + "px";
      const mouseBounds = mouse.getBoundingClientRect(), clientX = mouseBounds.left, clientY = mouseBounds.top, eventTarget = DomHelper.elementFromPoint(clientX, clientY);
      if (eventTarget !== me.prevTarget) {
        if (me.prevTarget) {
          me.mouseOutElements.push(me.prevTarget);
          if (!DomHelper.isDescendant(me.mouseOutElements[0], eventTarget)) {
            me.mouseOutElements.forEach((element) => me.triggerEvent(element, "mouseout"));
            me.mouseOutElements.length = 0;
          }
        }
        me.prevTarget = eventTarget;
        me.triggerEvent(eventTarget, "mouseover");
      }
      me.triggerEvent(eventTarget, step.action, {
        clientX,
        clientY
      });
    }, 50);
  }
  // target can be a string selector, a function or blank to use last target or outerElement if first time
  getTarget(step) {
    const me = this, target = step.target;
    if (!target) {
      return me.prevTarget || me.outerElement;
    }
    if (typeof target === "function") {
      return target(step);
    }
    return document.querySelector(target);
  }
  // action can be a function, a string or extracted from a property by scanning for unknown names
  normalizeStep(step) {
    if (step.action) {
      if (typeof step.action === "function") {
        return step.action(step);
      }
      return step;
    }
    if (typeof step === "function") {
      step();
      return step;
    }
    for (const prop in step) {
      if (Object.hasOwnProperty.call(step, prop) && !knownProps.includes(prop)) {
        step.action = prop.toLowerCase();
        step.to = step[prop];
      }
    }
    if (!step.target && (typeof step.to === "string" || typeof step.to === "function"))
      step.target = step.to;
    return step;
  }
  get isScrolling() {
    const me = this, box = me.outerElement.getBoundingClientRect(), scrolled = me.lastTop && box.top !== me.lastTop;
    me.lastTop = box.top;
    return scrolled;
  }
  get isInView() {
    const box = this.outerElement.getBoundingClientRect();
    return box.top < globalThis.innerHeight && box.bottom > 0;
  }
  get shouldPause() {
    return !this.isInView || this.isScrolling || document.hidden || !document.hasFocus();
  }
  // process the next step
  nextStep() {
    const me = this;
    if (me.shouldPause) {
      return;
    }
    if (me.currentStep === me.steps.length) {
      if (me.repeat) {
        me.currentStep = 0;
      } else {
        return me.abort(true);
      }
    }
    if (me.currentStep === 0) {
      me.trigger("initialize");
    }
    const mouse = me.mouse, step = me.normalizeStep(me.steps[me.currentStep++]), target = me.getTarget(step), action = step.action;
    if (target && action) {
      mouse.className = "simulated-mouse";
      if (action === "mousemove") {
        me.handleMouseMove(step, target);
      } else {
        if (target !== me.prevTarget) {
          const rect = Rectangle.from(target, me.outerElement);
          mouse.style.left = rect.x + rect.width / 2 + "px";
          mouse.style.top = rect.y + rect.height / 2 + "px";
        }
        if (action === "mousedown") {
          me.mouseDown = true;
        }
        if (action === "mouseup") {
          me.mouseDown = false;
        }
        me.timeoutId = me.setTimeout(() => {
          me.prevTarget = target;
          mouse.classList.add(action);
          if (action === "type") {
            const field = Widget.fromElement(target), parts = step.text.split("|");
            field.value = parts[parts.length === 1 || field.value != parts[0] ? 0 : 1];
          } else {
            me.triggerEvent(target, action);
          }
        }, action === "type" ? 100 : 550);
      }
    }
  }
};
DemoBot._$name = "DemoBot";

// ../Core/lib/Core/mixin/Fencible.js
var { defineProperty: defineProperty7 } = Object;
var fencibleSymbol = Symbol("fencible");
var NONE = [];
var distinct = (array) => Array.from(new Set(array));
var parseNames = (names2) => names2 ? distinct(StringHelper.split(names2)) : NONE;
var Fencible_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    static setupFenced(cls, meta) {
      const { fenced } = cls, methods = meta.getInherited("fenced");
      let all, any, lock, methodName, options;
      for (methodName in fenced) {
        options = fenced[methodName];
        if (options === true) {
          options = methodName;
        }
        if (!ObjectHelper.isObject(options)) {
          options = {
            all: options
          };
        }
        all = parseNames(options.all);
        any = parseNames(options.any);
        lock = options.lock ? parseNames(options.lock) : distinct(all.concat(any));
        any = any.length ? any : null;
        methods[methodName] = { any, all, lock };
      }
    }
    construct(...args) {
      const me = this, proto4 = Object.getPrototypeOf(me), fenced = me.$meta.getInherited("fenced"), fences = me[fencibleSymbol] = {}, isFree = (key) => !fences[key];
      ObjectHelper.forEach(fenced, ({ any, all, lock }, name) => {
        defineProperty7(me, name, {
          configurable: true,
          value(...params) {
            if (all.every(isFree) && (!any || any.some(isFree))) {
              let key;
              try {
                for (key of lock) {
                  fences[key] = (fences[key] || 0) + 1;
                }
                return proto4[name].apply(me, params);
              } finally {
                for (key of lock) {
                  --fences[key];
                }
              }
            }
          }
        });
      });
      return super.construct(...args);
    }
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "Fencible"), __publicField(_a5, "declarable", [
    /**
     * This class property returns an object that specifies the instance methods which need reentrancy protection.
     *
     * It is used like so:
     * ```javascript
     *  class Foo extends Base.mixin(Fencible) {
     *      static fenced = {
     *          reentrantMethod : true
     *      };
     *
     *      reentrantMethod() {
     *          // things() may cause reentrantMethod() to be called...
     *          // but we won't be allowed to reenter this method since we are already inside it
     *          this.things();
     *      }
     *  }
     * ```
     *
     * This can also be used to protect mutually reentrant method groups:
     *
     * ```javascript
     *  class Foo extends Base.mixin(Fencible) {
     *      static fenced = {
     *          foo : 'foobar'
     *          bar : 'foobar'
     *      };
     *
     *      foo() {
     *          console.log('foo');
     *          this.bar();
     *      }
     *
     *      bar() {
     *          console.log('bar');
     *          this.foo();
     *      }
     *  }
     *
     *  instance = new Foo();
     *  instance.foo();
     *  >> foo
     *  instance.bar();
     *  >> bar
     * ```
     *
     * The value for a fenced method value can be `true`, a string, an array of strings, or a
     * {@link #typedef-MethodFence} options object.
     *
     * Internally these methods are protected by defining a wrapper function on the instance. The class methods
     * remain on the class prototype and are guarded by the instance-level method. This allows these methods to use
     * `super` calls, just like other methods.
     *
     * @static
     * @member {Object} fenced
     * @internal
     */
    "fenced"
  ]), _a5;
};

// ../Core/lib/Core/helper/util/InfinityAxis.js
var { floor, max, min: min2 } = Math;
var constrain = (v, v0, v1) => min2(max(v, v0), v1);
var Axis = class extends Base.mixin(Fencible_default) {
  get direction() {
    const { name } = this, { events, scrollStarted } = this.owner;
    let d = 0, i, v, xy2;
    for (i = events.length; i-- > 0; ) {
      xy2 = events[i][name];
      if (v != null) {
        d = v - xy2;
        if (d) {
          break;
        }
      }
      v = xy2;
    }
    if (!d && v != null) {
      d = v - scrollStarted[name];
    }
    return d;
  }
  get drift() {
    return this._drift || 0;
  }
  get driftRange() {
    const { range } = this;
    return [range[0], max(range[0], range[1] - this.scrollSize)];
  }
  get items() {
    var _a5;
    return (_a5 = this.owner) == null ? void 0 : _a5.items;
  }
  get safeZone() {
    const { drift, scrollSize } = this, safetyMargin = floor(this.owner.safetyMargin * scrollSize);
    return [drift + safetyMargin, drift + scrollSize - safetyMargin];
  }
  get scroller() {
    var _a5, _b2;
    return (_b2 = (_a5 = this.owner) == null ? void 0 : _a5.client) == null ? void 0 : _b2.scrollable;
  }
  get scrollLimit() {
    var _a5;
    return (_a5 = this.owner) == null ? void 0 : _a5.scrollLimit;
  }
  get vast() {
    const { range } = this;
    return range ? range[1] - range[0] > this.scrollSize : false;
  }
  changePos(pos) {
    const { range } = this;
    return constrain(pos || 0, range[0], range[1]);
  }
  updatePos(pos) {
    this.sync({ pos });
  }
  updateDrift(drift) {
    const me = this, { pos, range } = me, scrollPos = pos - drift;
    if (me.scrollPos === scrollPos) {
      const event = new CustomEvent("scroll", { detail: "driftChange" });
      me.owner.scroller.element.dispatchEvent(event);
    } else if (range[0] <= pos && pos < range[1]) {
      me.scrollPos = scrollPos;
    }
  }
  changeRange(v, was) {
    const [lo, hi] = Array.isArray(v) ? v : [0, v], ret = [lo || 0, hi == null ? Infinity : hi];
    if (!was || ret[0] !== was[0] || ret[1] !== was[1]) {
      return ret;
    }
  }
  updateRange(range) {
    this.scrollSize = range[1] - range[0];
    this.sync({ range });
  }
  changeScrollSize(value2) {
    return min2(value2, this.scrollLimit);
  }
  snap() {
    const me = this, { direction, items: items2, owner, pos, range } = me;
    let item, p0, p1, snap, snapThreshold;
    for (item of items2) {
      [p0, p1] = me.getItemSpan(item);
      if (p0 <= pos && pos < p1) {
        if (p0 < pos) {
          snapThreshold = owner.snapThreshold;
          if (snapThreshold < 1) {
            snapThreshold = snapThreshold * (p1 - p0);
          }
          snapThreshold = max(1, snapThreshold);
          if (direction < 0) {
            snap = p1 - pos > snapThreshold ? p0 : p1;
          } else {
            snap = pos - p0 > snapThreshold ? p1 : p0;
          }
          snap = constrain(snap, range[0], range[1]);
          me.pos = snap;
          owner.watchForEnd();
        } else {
          owner.scrolling = false;
        }
        break;
      }
    }
  }
  sync(updates) {
    const me = Object.assign(this, updates), { _drift: driftWas, range, vast } = me;
    if (range && range[0] !== range[1]) {
      let { pos } = me, drift;
      const outOfRange = pos < range[0] || pos >= range[1];
      if (pos == null || outOfRange) {
        pos = range[1] < 0 ? range[0] : constrain(0, range[0], range[1]);
      }
      if (!vast) {
        drift = range[0];
      } else {
        const { driftRange, safeZone, scrollSize } = me;
        if (driftWas == null || pos < safeZone[0] || pos > safeZone[1]) {
          drift = constrain(floor(pos - scrollSize / 2), driftRange[0], driftRange[1]);
        } else {
          drift = driftWas;
        }
      }
      me.scroller.positionDirty = true;
      me.overflow = "hidden-scroll";
      me.drift = drift;
      if (drift !== driftWas) {
        if (outOfRange) {
          me.positionDirty = false;
          me._pos = null;
          me.pos = pos;
        } else {
          me.positionDirty = true;
        }
      } else if ((updates == null ? void 0 : updates.pos) != null) {
        me.scrollPos = pos - drift;
        me.positionDirty = true;
      } else {
        if (me.positionDirty) {
          me.positionDirty = false;
          me._pos = null;
        }
        me.pos = me.scrollPos + drift;
      }
      if ((updates == null ? void 0 : updates.range) || drift !== driftWas) {
        me.owner.syncItems();
      }
    } else {
      me.overflow = "hidden";
    }
  }
  toLogical(physical) {
    return physical + this.drift;
  }
  /**
   * Returns the physical scroll position for the given logical scroll position. Valid physical scroll positions are
   * not negative. This method returns NaN for logical scroll positions that cannot be converted to a valid, physical
   * scroll position.
   * @param {Number} logical
   * @returns {Number}
   */
  toPhysical(logical) {
    const physical = logical - this.drift;
    return physical >= 0 && physical < this.scrollLimit ? physical : NaN;
  }
};
__publicField(Axis, "configurable", {
  /**
   * The owning `InfinityScroller` for this axis.
   *
   * @config {Core.helper.util.InfinityScroller}
   */
  owner: null,
  /**
   * The delta between a logical scroll position and physical scroll position. In other words, adding the drift
   * to a logical scroll position, produces the corresponding physical scroll position.
   * @config {Number}
   */
  drift: null,
  overflow: null,
  /**
   * The logical scroll position.
   * @config {Number}
   */
  pos: {
    value: null,
    default: 0,
    $config: "lazy"
  },
  /**
   * The logical scroll range as a two element array containing the range's lower bound and upper bound,
   * respectively.
   * @config {Number[]}
   */
  range: {
    value: null,
    $config: "lazy"
  },
  scrollSize: null
});
__publicField(Axis, "fenced", {
  sync: true
});
var XAxis = class extends Axis {
  get name() {
    return "x";
  }
  get other() {
    var _a5;
    return (_a5 = this.owner) == null ? void 0 : _a5.y;
  }
  get scrollPos() {
    return this.scroller.x;
  }
  set scrollPos(v) {
    this.scroller.x = v;
  }
  get scrollSize() {
    return this.scroller.scrollWidth;
  }
  set scrollSize(v) {
    super.scrollSize = v;
  }
  getItemSpan(item) {
    const vxy = item.$vxy, { x } = vxy;
    return [x, x + vxy.width];
  }
  updateScrollSize(v) {
    this.scroller.scrollWidth = v;
  }
  updateOverflow(overflow) {
    this.scroller.overflowX = overflow;
  }
};
var YAxis = class extends Axis {
  get name() {
    return "y";
  }
  get other() {
    var _a5;
    return (_a5 = this.owner) == null ? void 0 : _a5.x;
  }
  get scrollPos() {
    return this.scroller.y;
  }
  set scrollPos(v) {
    this.scroller.y = v;
  }
  get scrollSize() {
    return this.scroller.scrollHeight;
  }
  set scrollSize(v) {
    super.scrollSize = v;
  }
  getItemSpan(item) {
    const vxy = item.$vxy, { y } = vxy;
    return [y, y + vxy.height];
  }
  updateScrollSize(v) {
    this.scroller.scrollHeight = v;
  }
  updateOverflow(overflow) {
    this.scroller.overflowY = overflow;
  }
};
Axis.X = XAxis;
Axis.Y = YAxis;
Axis._$name = "Axis";

// ../Core/lib/Core/helper/util/InfinityScroller.js
var infinityCls = "b-infinity-scroller";
var NO_SIZE = Object.freeze({ width: 0, height: 0 });
var addVirtualPos = (widget, name) => {
  const prop = "v" + name;
  if (!(prop in widget)) {
    Object.defineProperty(widget, prop, {
      configurable: true,
      get() {
        var _a5;
        return (_a5 = this.$vxy) == null ? void 0 : _a5[name];
      },
      set(v) {
        if (this.$vxy) {
          this.$vxy[name] = v;
        }
      }
    });
  }
};
var Virtualizer = class extends Base {
  get height() {
    return this.getDim("height");
  }
  get width() {
    return this.getDim("width");
  }
  get x() {
    return this._x;
  }
  set x(x) {
    this.moveTo(x);
  }
  get xy() {
    return [this._x, this._y];
  }
  set xy(xy2) {
    this.moveTo(...xy2);
  }
  get y() {
    return this._y;
  }
  set y(y) {
    this.moveTo(null, y);
  }
  getDim(dim) {
    const me = this, { widget } = me, cachedValue = "_" + dim;
    return widget ? widget.monitorResize ? widget[dim] : me[cachedValue] || (me[cachedValue] = widget[dim]) : 0;
  }
  moveTo(x, y) {
    if (x != null) {
      this._x = x;
    }
    if (y != null) {
      this._y = y;
    }
    this.sync();
  }
  sync() {
    const me = this, { owner, widget } = me, { element } = widget, x = owner.x.toPhysical(me.x), y = owner.y.toPhysical(me.y), hide = isNaN(x + y);
    me.hidden = hide;
    if (hide) {
      me.size = NO_SIZE;
    } else {
      element.style.transform = `translate(${x}px, ${y}px)`;
    }
  }
  updateHidden(hidden) {
    var _a5, _b2;
    (_b2 = (_a5 = this.widget) == null ? void 0 : _a5.element) == null ? void 0 : _b2.classList.toggle("b-scroll-hidden", hidden);
  }
  updateWidget(widget, was) {
    was == null ? void 0 : was.element.classList.remove("b-infinity-scroller-item");
    if (widget) {
      widget.element.classList.add("b-infinity-scroller-item");
      addVirtualPos(widget, "x");
      addVirtualPos(widget, "y");
      addVirtualPos(widget, "xy");
    }
  }
};
__publicField(Virtualizer, "configurable", {
  hidden: null,
  owner: null,
  widget: null
});
__publicField(Virtualizer, "prototypeProperties", {
  _height: 0,
  _width: 0,
  _x: 0,
  _y: 0
});
var InfinityScroller = class extends Base.mixin(Events_default) {
  /**
   * Fired when scrolling happens on this InfinityScroller's element.
   * @event scroll
   * @param {Event} relatedEvent The DOM event.
   * @param {Core.widget.Container} widget The owning {@link #config-client} instance.
   * @param {Core.helper.util.InfinityScroller} source This InfinityScroller
   */
  /**
   * Fired when scrolling finished on this InfinityScroller's element.
   * @event scrollEnd
   * @param {Event} relatedEvent The last DOM event to fire.
   * @param {Core.widget.Container} widget The owning {@link #config-client} instance.
   * @param {Core.helper.util.InfinityScroller} source This InfinityScroller
   */
  /*
   * break - above jsdoc does not describe the next method!
   */
  get element() {
    var _a5;
    return (_a5 = this.scroller) == null ? void 0 : _a5.element;
  }
  get events() {
    return this._events || (this._events = []);
  }
  get items() {
    var _a5;
    return (((_a5 = this.client) == null ? void 0 : _a5.items) || []).filter((it) => it.$vxy);
  }
  get lastEvent() {
    var _a5;
    return (_a5 = this._events) == null ? void 0 : _a5[this._events.length - 1];
  }
  get pos() {
    return [this.x.pos, this.y.pos];
  }
  changeScrollLimit(limit) {
    return limit || DomHelper.scrollLimit;
  }
  updateAnimate(animate, was) {
    var _a5;
    const classList = (_a5 = this.element) == null ? void 0 : _a5.classList;
    was && (classList == null ? void 0 : classList.remove(`b-infinity-scroller-${was}`));
    animate && (classList == null ? void 0 : classList.add(`b-infinity-scroller-${animate}`));
  }
  updateClient(client) {
    this.scroller = (client == null ? void 0 : client.scrollable) || null;
  }
  updateScroller(scroller, was) {
    var _a5;
    this.detachListeners("scroller");
    (_a5 = was == null ? void 0 : was.element) == null ? void 0 : _a5.classList.remove(infinityCls);
    if (scroller) {
      scroller.element.classList.add(infinityCls);
      scroller.ion({
        thisObj: this,
        name: "scroller",
        scroll: "onInternalScroll"
      });
    }
  }
  changeX(axis) {
    return Axis.X.new(axis, {
      owner: this
    });
  }
  changeY(axis) {
    return Axis.Y.new(axis, {
      owner: this
    });
  }
  onInternalScroll(ev) {
    const me = this, { lastPos, scrolling } = me;
    if (!scrolling) {
      me.scrolling = "maybe";
    }
    me.sync();
    const curPos = [me.x.pos, me.y.pos];
    if (lastPos && lastPos[0] === curPos[0] && lastPos[1] === curPos[1]) {
      me.scrolling = scrolling;
    } else {
      me.lastPos = curPos;
      me.pushEvent(ev);
      if (!scrolling) {
        me.scrolling = true;
      }
      me.triggerScroll("scroll", ev);
    }
  }
  onInternalScrollEnd() {
    const me = this, { scrolling } = me;
    if (scrolling) {
      if (scrolling === "snap") {
        me.updateScrolling(scrolling);
      } else {
        me.scrolling = me.snap ? "snap" : false;
      }
    }
  }
  /**
   * Tracks the given `event` and a small number of prior events. These are used to determine direction of scroll in
   * the scroll axis, as well as to always have access to the `lastEvent` responsible for scrolling.
   * @param {Event} event
   * @private
   */
  pushEvent(event) {
    const me = this, { events } = me, wrapper = { event, x: me.x.pos, y: me.y.pos }, last = events[events.length - 1];
    if (event.type === (last == null ? void 0 : last.event.type) && last.x === wrapper.x && last.y === wrapper.y) {
      last.event = event;
    } else {
      if (!last) {
        me.scrollStarted = wrapper;
      }
      events.push(wrapper);
      while (events.length > 3) {
        events.shift();
      }
    }
  }
  /**
   * Scrolls to the specified `x` and `y` positions, or by the specified `dx` and `dy`. Only one of these values is
   * required. It is invalid to pass `x` and `dx`, or `y` and `dy` at the same time, however, it is valid to pass
   * `x` and `dy`, or `y` and `dx`.
   *
   * This method returns a Promise that resolves to a boolean value. This value indicates that the desired scroll
   * adjustment was made. It is `false` if the scroll ends at a different position. This can happen if the user
   * interferes with the scroll, or another call to this method is made before the first call completes.
   *
   * @param {Object} options
   * @param {Boolean} [options.animate] Specify `false` to scroll without animation.
   * @param {Number} [options.dx] The amount by which the `x` scroll position should be adjusted.
   * @param {Number} [options.dy] The amount by which the `y` scroll position should be adjusted.
   * @param {Number} [options.x] The new value for the `x` scroll position.
   * @param {Number} [options.y] The new value for the `y` scroll position.
   * @returns {Boolean}
   * @async
   */
  scrollTo(options) {
    var _a5, _b2;
    const me = this, { scrollingTo } = me, cx = me.x.pos, cy = me.y.pos, x = (_a5 = options == null ? void 0 : options.x) != null ? _a5 : cx + (options == null ? void 0 : options.dx) || 0, y = (_b2 = options == null ? void 0 : options.y) != null ? _b2 : cy + (options == null ? void 0 : options.dy) || 0, dx = x - cx, dy = y - cy, delta = dx || dy, animate = (options == null ? void 0 : options.animate) !== false && (options == null ? void 0 : options.animate) !== null;
    if (scrollingTo) {
      if (scrollingTo.scroll.x === x && scrollingTo.scroll.y === y && scrollingTo.scroll.animate === animate) {
        return scrollingTo.promise;
      }
      scrollingTo.finish();
    }
    const scrollTo = me.scrollingTo = Promissory.new();
    scrollTo.scroll = { x, y, animate };
    delta && me.unanimated(!animate, () => {
      if (dx) {
        me.x.pos = x;
      }
      if (dy) {
        me.y.pos = y;
      }
    });
    if (delta && animate) {
      scrollTo.cancel = me.ion({
        once: true,
        scrollEnd() {
          scrollTo.resolve(me.x.pos === x && me.y.pos === y);
        }
      });
      scrollTo.finish = () => {
        if (scrollTo.cancel) {
          scrollTo.cancel();
          scrollTo.cancel = null;
          scrollTo.resolve(false);
          me.scrollStarted = null;
        }
      };
    } else {
      scrollTo.resolve(true);
    }
    scrollTo.finally(() => {
      if (me.scrollingTo === scrollTo) {
        me.scrollingTo = null;
      }
    });
    return scrollTo.promise;
  }
  suppressAnim(suppress) {
    const me = this, suppressedWas = me._suppressAnim || 0, suppressed = me._suppressAnim = Math.max(0, suppressedWas + (suppress ? 1 : -1));
    me.scroller.behavior = suppressed ? "instant" : null;
  }
  sync() {
    this.unanimated(() => {
      this.x.sync();
      this.y.sync();
    });
  }
  syncItems() {
    for (const item of this.items) {
      item.$vxy.sync();
    }
  }
  triggerScroll(eventName, relatedEvent, x = this.x.pos, y = this.y.pos) {
    this.trigger(eventName, { widget: this.client, relatedEvent, x, y });
  }
  updateScrolling(scrolling, was) {
    const me = this, maybe = was === "maybe", { lastEvent } = me;
    if (!scrolling) {
      if (was && ((lastEvent == null ? void 0 : lastEvent.event.detail) === "driftChange" || !maybe)) {
        me.scrollEnded = lastEvent;
        me.events.length = 0;
        me.triggerScroll("scrollEnd", lastEvent.event, lastEvent.x, lastEvent.y);
        me.element.classList.remove("b-scrolling");
      }
    } else if (maybe) {
      me.watchForEnd();
      me.scrollEnded = null;
      me.triggerScroll("scrollStart", lastEvent.event, lastEvent.x, lastEvent.y);
      me.element.classList.add("b-scrolling");
    } else if (scrolling === "snap") {
      me[me.snap].snap();
    }
  }
  unanimated(suppressAnim, fn2) {
    if (typeof suppressAnim === "function") {
      fn2 = suppressAnim;
      suppressAnim = true;
    }
    if (!suppressAnim) {
      fn2();
    } else {
      this.suppressAnim(true);
      try {
        fn2();
      } finally {
        this.suppressAnim(false);
      }
    }
  }
  virtualize(item) {
    if (typeof item === "string") {
      item = this.client.widgetMap[item];
    }
    let ret = item.$vxy;
    if (!ret || ret.owner !== this) {
      ret == null ? void 0 : ret.destroy();
      item.$vxy = ret = new Virtualizer({
        owner: this,
        widget: item
      });
    }
    return ret;
  }
  watchForEnd() {
    const me = this, { client, scrollIdle, scrollStarted } = me;
    let sinceChange = 0, prev, xy2;
    (function check() {
      if (!me.isDestroyed && !client.isDestroyed && scrollStarted === me.scrollStarted) {
        xy2 = me.pos;
        if (!prev || prev[0] !== xy2[0] || prev[1] !== xy2[1]) {
          prev = xy2;
          sinceChange = 0;
        } else if (++sinceChange >= scrollIdle && !GlobalEvents_default.hasActiveTouches) {
          me.onInternalScrollEnd();
          return;
        }
        client.requestAnimationFrame(check);
      }
    })();
  }
};
__publicField(InfinityScroller, "configurable", {
  /**
   * Set to `null` to disable smooth scroll animation.
   * @prp {'smooth'|null}
   * @default
   */
  animate: "smooth",
  /**
   * The owning container. Because position virtualization is only implemented for {@link Core.widget.Widget},
   * the `client` must be a {@link Core.widget.Container}.
   * @config {Core.widget.Container}
   * @internal
   */
  client: null,
  /**
   * The proportional amount of the browser's true {@link Core.helper.DomHelper#property-scrollLimit-static} to
   * not use in order to reduce likelihood of browser anomalies. By default, 99% of the limit is used (this
   * value being 1%, by default).
   * @prp {Number}
   * @default
   */
  safetyMargin: 0.01,
  // 1% of 18M = 180K (should be larger than visual range)
  scroller: null,
  /**
   * The number of consecutive animation frames with no scroll motion that must occur before the scroll is
   * declared complete. After this time, the {@link #event-scrollEnd} event is fired.
   * @prp {Number}
   * @internal
   */
  scrollIdle: 8,
  // Tests fail in Firefox if this value is 5
  /**
   * This config is used to track the current scrolling state. As it changes, various side-effects ensue.
   * @prp {Boolean|'maybe'|'snap'}
   * @private
   * @default false
   */
  scrolling: null,
  /**
   * Used for unit testing. Ignores the browser scroll limit in favor of a consistent, predictable value.
   * @internal
   */
  scrollLimit: {
    value: 0,
    $config: "lazy"
  },
  /**
   * If not null, this config determines which axis to use for scroll snapping.
   * @prp {'x'|'y'|null}
   */
  snap: null,
  /**
   * The minimum number of pixels or the minimum proportion of the item that must be scrolled to initiate a
   * snap to reveal the whole item. A completed scroll of less than this amount is reversed. If this value is
   * less-than 1, then this is the decimal fraction of the item's size. By default, this value is 0.1, which
   * is 10% of the item's width. Otherwise, this value is an absolute number of pixels.
   * @prp {Number}
   */
  snapThreshold: 0.1,
  /**
   * The {@link Core.helper.util.InfinityAxis} instance managing the x-axis.
   * @prp {Core.helper.util.InfinityAxis}
   * @accepts {InfinityAxisConfig}
   */
  x: {},
  /**
   * The {@link Core.helper.util.InfinityAxis} instance managing the y-axis.
   * @prp {Core.helper.util.InfinityAxis}
   * @accepts {InfinityAxisConfig}
   */
  y: {}
});
InfinityScroller._$name = "InfinityScroller";

// ../Core/lib/Core/helper/util/InfinityScrollable.js
var InfinityScrollable_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    /**
     * Returns the default config object for the `infinityScroller`. It is likely that a derived class will need to
     * augment this, but be sure to include the value produced by calling `super`.
     * @property {InfinityScrollerConfig}
     * @readonly
     * @internal
     */
    get infinityScrollerDefaults() {
      return {
        animate: this.animate
      };
    }
    compose() {
      return {};
    }
    changeInfinityScroller(config, existing) {
      if (existing) {
        existing.setConfig(config);
      } else {
        existing = InfinityScroller.new({ client: this }, this.infinityScrollerDefaults, config);
      }
      return existing;
    }
    onChildAdd(item) {
      var _a6;
      item.innerItem && ((_a6 = this._infinityScroller) == null ? void 0 : _a6.virtualize(item));
      return super.onChildAdd(item);
    }
    onInfiniteScroll() {
      this.syncSoon();
    }
    onInfiniteScrollEnd() {
      this.syncSoon.now();
    }
    onInfiniteScrollStart() {
    }
    onInternalResize(element, width, height, oldWidth, oldHeight) {
      super.onInternalResize(element, width, height, oldWidth, oldHeight);
      this.hasPainted && this.syncSoon();
    }
    onInternalPaint(info) {
      var _a6;
      (_a6 = super.onInternalPaint) == null ? void 0 : _a6.call(this, info);
      if (info.firstPaint) {
        this.syncSoon.now();
        this.infinityScroller.ion({
          thisObj: this,
          scroll: "onInfiniteScroll",
          scrollStart: "onInfiniteScrollStart",
          scrollEnd: "onInfiniteScrollEnd"
        });
      }
    }
    get scrollingTo() {
      return this.infinityScroller.scrollingTo;
    }
    scrollTo(options) {
      return this.infinityScroller.scrollTo(options);
    }
    /**
     * This method is called on animation frames to synchronize with the state of the `infinityScroller`. This method
     * is called on the first `onPaint` as well as when the widget resizes.
     *
     * This method is not called directly. It is only called by {@link #function-syncSoon} which allows calls to
     * aggregate to the next animation frame.
     * @internal
     */
    sync() {
      this.infinityScroller.sync();
    }
    /**
     * Call this method to schedule a synchronization pass for the scroller. This will call {@link #function-sync} on
     * the next animation frame.
     */
    syncSoon() {
      const me = this;
      if (me.isPainted) {
        me.recompose.flush();
        me.infinityScroller.animate = me.animate;
        me.sync();
      }
    }
    unanimated(suppressAnim, fn2) {
      return this.infinityScroller.unanimated(suppressAnim, fn2);
    }
    updateAnimation() {
      this._infinityScroller && this.syncSoon();
    }
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement thus.
    get widgetClass() {
    }
  }, __publicField(_a5, "configurable", {
    /**
     * Set to `null` to disable smooth scroll animation.
     * @prp {'smooth'|null}
     * @default
     */
    animate: "smooth",
    /**
     * The lazily created `InfinityScroller`. For derived classes, see {@link #property-infinityScrollerDefaults}.
     * @prp {Core.helper.util.InfinityScroller}
     * @accepts {InfinityScrollerConfig}
     * @internal
     */
    infinityScroller: {
      value: {},
      $config: "lazy"
    },
    monitorResize: true,
    scrollable: {}
    // enable DOM scroller
  }), __publicField(_a5, "delayable", {
    syncSoon: "raf"
  }), _a5;
};

// ../Core/lib/Core/helper/util/Navigator.js
var Navigator = class extends Base.mixin(Events_default, Factoryable_default) {
  static get configurable() {
    return {
      /**
       * The owning Widget which is using this Navigator.
       * @config {Core.widget.Widget}
       */
      ownerCmp: null,
      /**
       * If the items in the owning widget are naturally tabbable, then the Navigator does not
       * need to listen for navigation keys and move focus. It just reacts to natural focus
       * movement.
       * @config {Boolean}
       */
      itemsTabbable: null,
      /**
       * The encapsulating element in which navigation takes place.
       * @config {HTMLElement}
       */
      target: null,
      /**
       * The element which provides key events for navigation. Optional. Defaults to the {@link #config-target} element.
       * @config {HTMLElement}
       */
      keyEventTarget: null,
      /**
       * An optional key event processor which may preprocess the key event. Returning `null` prevents processing of the event.
       * @param {KeyboardEvent} event
       * @returns {Boolean|null}
       * @config {String|Function}
       */
      processEvent: null,
      /**
       * A query selector which identifies descendant elements within the {@link #config-target} which are navigable.
       * @config {String}
       */
      itemSelector: null,
      /**
       * The currently focused element within the {@link #config-target}.
       * @config {HTMLElement}
       */
      activeItem: null,
      /**
       * A CSS class name to add to focused elements.
       * @config {String}
       * @default
       */
      focusCls: "b-active",
      /**
       * An object containing key definitions keyed by the key name eg:
       *
       * ```javascript
       *  keys : {
       *      "CTRL+Space" : 'onCtrlSpace',
       *      Enter        : 'onEnterKey'
       *  }
       * ```
       *
       * The {@link #config-ownerCmp} is used as the `this` reference and to resolve string method names.
       *
       * The following arguments are passed to the handling method:
       * - keyEvent : The key event.
       * - navigator : This Navigator instance.
       * - activeItem : The currently focused element.
       *
       * Modified key names must be created prepending one or more `'CTRL+'`, `'SHIFT+'`, `'ALT+'`
       * in that order, for example `"CTRL+SHIFT+Enter" : 'showMenu'`
       * @config {Object<String,String|Function>}
       * @default
       */
      keys: null,
      /**
       * Configure as `true` to also navigate when the `CTRL` modifier key is used along with
       * navigation keys.
       * @config {Boolean}
       * @default false
       */
      allowCtrlKey: null,
      /**
       * Configure as `true` to also navigate when the `SHIFT` modifier key is used along with
       * navigation keys.
       * @config {Boolean}
       * @default false
       */
      allowShiftKey: null,
      /**
       * Configure as `true` to activate items on mouseover. This is used by the Combo
       * field when using a List as its dropdown.
       * @config {Boolean}
       */
      activateOnMouseover: null,
      scrollDuration: 50,
      /**
       * Configure as, or set to `true` to disable the processing of keys.
       * @config {Boolean}
       */
      disabled: null,
      datasetIdProperty: "id",
      /**
       * The number of columns if the navigable items are in a flexbox or grid layout.
       *
       * This is used to determine the action taken when using UP and DOWN arrow keys.
       *
       * This is calculated automatically from the `grid-template-columns` style of the item container if possible.
       * If using a flexbox layout, and there is a regular number of columns on every row, specify the number of columns
       * there will be in each row.
       * @config {Number} [columnCount]
       */
      columnCount: null,
      testConfig: {
        scrollDuration: 1
      }
    };
  }
  static get factoryable() {
    return {
      defaultType: "navigator"
    };
  }
  get activeItem() {
    const { _activeItem } = this;
    if (this.target.contains(_activeItem)) {
      return _activeItem;
    }
    this._activeItem = null;
  }
  updateOwnerCmp(ownerCmp) {
    if (!this.itemSelector) {
      this.itemSelector = `.${ownerCmp.itemCls}:not([data-noselect])`;
    }
    if (!ownerCmp.itemsFocusable) {
      DomHelper.setAttributes(this.keyEventTarget, {
        "aria-activedescendant": `${this.owner.id}-active-descendant`
      });
    }
  }
  set navigationEvent(navigationEvent) {
    const { owner } = this;
    this._navigationEvent = owner.navigationEvent = navigationEvent;
    if (navigationEvent) {
      Promise.resolve(1).then(() => this.navigationEvent = null);
    }
  }
  get navigationEvent() {
    return this._navigationEvent;
  }
  static getComposedKeyName(keyEvent) {
    const keyName = (keyEvent.key || "").trim() || keyEvent.code;
    return `${keyEvent.ctrlKey ? "CTRL+" : ""}${keyEvent.shiftKey ? "SHIFT+" : ""}${keyEvent.altKey ? "ALT+" : ""}${keyName}`;
  }
  onKeyDown(keyEvent) {
    var _a5;
    const me = this, {
      ownerCmp,
      itemSelector,
      activeItem,
      itemsTabbable
    } = me, { target } = keyEvent, firstItem = me.firstItem, keyName = (keyEvent.key || "").trim() || keyEvent.code, composedKeyName = me.constructor.getComposedKeyName(keyEvent), validTarget = target.matches(itemSelector) || target === me.keyEventTarget;
    if (!me.disabled && me.processEvent) {
      keyEvent = me.callback(me.processEvent, me.thisObj || ownerCmp, [keyEvent]);
    }
    if (keyEvent && !me.disabled && firstItem && me.target.offsetParent && validTarget) {
      if (!itemsTabbable) {
        if (me.inlineFlow == null) {
          const itemContainer = me.itemContainer = firstItem.parentNode, containerDisplayStyle = DomHelper.getStyleValue(itemContainer, "display"), itemPositionStyle = DomHelper.getStyleValue(firstItem, "position"), itemDisplayStyle = DomHelper.getStyleValue(firstItem, "display"), itemFloatStyle = DomHelper.getStyleValue(firstItem, "float");
          if (!me.columnCount) {
            me.columnCount = DomHelper.getStyleValue(itemContainer, "grid-template-columns").split(" ").length;
          }
          me.inlineFlow = itemPositionStyle === "absolute" || itemDisplayStyle === "inline" || itemDisplayStyle === "inline-block" || itemFloatStyle === "left" || itemFloatStyle === "right" || containerDisplayStyle === "flex" && DomHelper.getStyleValue(itemContainer, "flex-direction") === "row" || containerDisplayStyle === "grid" && me.columnCount > 1;
          if (me.columnCount > 1) {
            me.prevRowKey = "ArrowUp";
            me.nextRowKey = "ArrowDown";
          }
        }
        if (!me.prevKey) {
          if (me.inlineFlow) {
            me.prevKey = "ArrowLeft";
            me.nextKey = "ArrowRight";
          } else {
            me.prevKey = "ArrowUp";
            me.nextKey = "ArrowDown";
          }
        }
      }
      me.navigationEvent = keyEvent;
      if (activeItem && !me.disabled) {
        switch (keyName) {
          case me.prevKey:
            if (keyEvent.ctrlKey && !me.allowCtrlKey || keyEvent.shiftKey && !me.allowShiftKey) {
              return;
            }
            keyEvent.preventDefault();
            keyEvent.handled = true;
            me.navigatePrevious(keyEvent);
            break;
          case me.nextKey:
            if (keyEvent.ctrlKey && !me.allowCtrlKey || keyEvent.shiftKey && !me.allowShiftKey) {
              return;
            }
            keyEvent.preventDefault();
            keyEvent.handled = true;
            me.navigateNext(keyEvent);
            break;
          case me.prevRowKey:
            {
              if (keyEvent.ctrlKey && !me.allowCtrlKey || keyEvent.shiftKey && !me.allowShiftKey) {
                return;
              }
              keyEvent.preventDefault();
              const siblings = Array.from(me.itemContainer.querySelectorAll(me.itemSelector));
              me.activeItem = siblings[Math.max(siblings.indexOf(activeItem) - me.columnCount, 0)];
              keyEvent.handled = true;
            }
            break;
          case me.nextRowKey:
            {
              if (keyEvent.ctrlKey && !me.allowCtrlKey || keyEvent.shiftKey && !me.allowShiftKey) {
                return;
              }
              keyEvent.preventDefault();
              const siblings = Array.from(me.itemContainer.querySelectorAll(me.itemSelector));
              me.activeItem = siblings[Math.min(siblings.indexOf(activeItem) + me.columnCount, siblings.length - 1)];
              keyEvent.handled = true;
            }
            break;
          default: {
            const keyHandler = (_a5 = me.keys) == null ? void 0 : _a5[composedKeyName];
            if (keyHandler && !me.disabled) {
              if (me.callback(keyHandler, me.thisObj || ownerCmp, [keyEvent, me, activeItem]) === false) {
                return;
              }
            } else if (ownerCmp.onInternalKeyDown) {
              ownerCmp.onInternalKeyDown(keyEvent);
            }
            if (DomHelper.getActiveElement(target) !== target) {
              keyEvent.preventDefault();
            }
            if (itemsTabbable) {
              return;
            }
          }
        }
      } else {
        if (itemsTabbable) {
          return;
        }
        if (keyName === me.nextKey || keyName === me.prevKey) {
          me.activeItem = me.getDefaultNavigationItem(keyEvent);
        }
      }
      if (me.activeItem !== activeItem) {
        me.triggerNavigate(keyEvent);
      }
    }
  }
  getDefaultNavigationItem(keyEvent) {
    const { target, itemSelector, prevKey, nextKey } = this;
    if (target.compareDocumentPosition(keyEvent.target) & 4 && keyEvent.key === prevKey) {
      return target.querySelector(`${itemSelector}:last-of-type`);
    }
    if (target.compareDocumentPosition(keyEvent.target) & 2 && keyEvent.key === nextKey) {
      return target.querySelector(`${itemSelector}`);
    }
  }
  navigatePrevious(keyEvent) {
    const me = this, previous = me.previous;
    keyEvent.preventDefault();
    if (previous) {
      me.ownerCmp.scrollable.scrollIntoView(previous, { animate: me.scrollDuration }).then(() => {
        me.activeItem = previous;
        me.triggerNavigate(keyEvent);
      });
    }
  }
  navigateNext(keyEvent) {
    const me = this, next = me.next;
    keyEvent.preventDefault();
    if (next) {
      me.ownerCmp.scrollable.scrollIntoView(next, { animate: me.scrollDuration }).then(() => {
        me.activeItem = next;
        me.triggerNavigate(keyEvent);
      });
    }
  }
  get owner() {
    return this.ownerCmp;
  }
  get firstItem() {
    return this.target.querySelector(this.itemSelector);
  }
  get previous() {
    return this.getAdjacent(-1);
  }
  get next() {
    return this.getAdjacent(1);
  }
  /**
   * Returns the next or previous navigable element starting from the passed `from` element,
   * navigating in the passed direction.
   * @param {HTMLElement} [from] The start point. Defaults to the current {@link #config-activeItem}
   * @param {Number} [direction=1] The direction. -1 for backwards, else forwards.
   */
  getAdjacent(direction = 1, from = this.activeItem) {
    const treeWalker = this.treeWalker;
    treeWalker.currentNode = from;
    treeWalker[direction < 0 ? "previousNode" : "nextNode"]();
    if (treeWalker.currentNode !== this.activeItem) {
      return treeWalker.currentNode;
    }
  }
  shouldIgnoreFocusIn(focusInEvent) {
    if (this.ignoreEvent === focusInEvent) {
      this.ignoreEvent = null;
      return true;
    }
    return focusInEvent.target.matches(".b-focus-trap");
  }
  onTargetFocusIn(focusInEvent) {
    var _a5;
    const me = this, {
      target,
      relatedTarget
    } = focusInEvent, {
      owner,
      itemsTabbable,
      skipScrollIntoView,
      previousActiveItem
    } = me;
    if (!me.shouldIgnoreFocusIn(focusInEvent)) {
      if (target.matches(me.itemSelector)) {
        me.skipScrollIntoView = true;
        me.activeItem = target;
        me.skipScrollIntoView = skipScrollIntoView;
        if (me.activeItem && itemsTabbable) {
          me.triggerNavigate(focusInEvent);
        }
      } else if (target === me.target) {
        const fromWhere = relatedTarget ? target.compareDocumentPosition(relatedTarget) : 0;
        if (fromWhere & Node.DOCUMENT_POSITION_CONTAINED_BY) {
          (_a5 = owner.previousSibling) == null ? void 0 : _a5.focus();
        } else {
          me.skipScrollIntoView = true;
          me.activeItem = previousActiveItem && me.target.contains(previousActiveItem) ? previousActiveItem : 0;
          me.skipScrollIntoView = skipScrollIntoView;
        }
      }
    }
  }
  onTargetFocusOut(focusOutEvent) {
    const me = this, { relatedTarget } = focusOutEvent;
    if (!(relatedTarget == null ? void 0 : relatedTarget.matches(".b-focus-trap"))) {
      if (!relatedTarget || !me.target.contains(relatedTarget) || !relatedTarget.matches(me.itemSelector)) {
        if (me.activeItem) {
          me.activeItem = null;
          if (me.itemsTabbable) {
            me.triggerNavigate(focusOutEvent);
          }
        }
      }
    }
  }
  onTargetMouseDown(mouseDownEvent) {
    const me = this, target = mouseDownEvent.target.closest(me.itemSelector);
    me.navigationEvent = mouseDownEvent;
    if (me.itemsTabbable) {
      if (target === me.activeItem) {
        me.triggerNavigate(mouseDownEvent);
      }
    } else if (me.ownerCmp.itemsFocusable === false) {
      me.onFocusGesture(mouseDownEvent);
    } else if (target) {
      mouseDownEvent.preventDefault();
      target.focus({ preventScroll: true });
    }
  }
  onTargetMouseover({ target }) {
    this.activeItem = target;
  }
  onTargetClick(clickEvent) {
    var _a5;
    const me = this, { skipScrollIntoView } = me;
    if (me.skipNextClick || ((_a5 = me.navigationEvent) == null ? void 0 : _a5.ignoreNavigation)) {
      me.skipNextClick = false;
      return;
    }
    if (me.disabled) {
      return;
    }
    me.skipScrollIntoView = me.activateOnMouseover;
    if (me.processEvent) {
      clickEvent = me.callback(me.processEvent, me.thisObj || me.ownerCmp, [clickEvent]);
    }
    if (clickEvent) {
      me.activeItem = clickEvent.target.closest(me.itemSelector);
      me.triggerNavigate(clickEvent);
    }
    me.skipScrollIntoView = skipScrollIntoView;
  }
  // We have to prevent focus moving from eg, an input field when we mousedown
  // or touchtap a non focusable item when ownerCmp has itemsFocusable: false.
  // If the event was not on an item, we must allow it through to allow Lists
  // to contain other widgets.
  onFocusGesture(event) {
    if (event.target === this.ownerCmp.contentElement || event.target.closest(this.itemSelector) && this.ownerCmp.itemsFocusable === false) {
      event.preventDefault();
    }
  }
  acceptNode(node) {
    return node.offsetParent && node.matches && node.matches(this.itemSelector) ? DomHelper.NodeFilter.FILTER_ACCEPT : DomHelper.NodeFilter.FILTER_SKIP;
  }
  changeActiveItem(activeItem) {
    if (activeItem != null) {
      if (this.ownerCmp.getItem) {
        activeItem = this.ownerCmp.getItem(activeItem);
      } else if (typeof activeItem === "number") {
        activeItem = this.target.querySelector(this.itemSelector + `:nth-child(${activeItem + 1})`);
      }
    }
    return (activeItem == null ? void 0 : activeItem.matches(this.itemSelector)) ? activeItem : null;
  }
  updateActiveItem(activeItem, oldActiveItem) {
    var _a5;
    const me = this, { ownerCmp } = me, isActive2 = oldActiveItem && me.target.contains(oldActiveItem), needsFocus = activeItem && DomHelper.isFocusable(activeItem) && activeItem !== DomHelper.getActiveElement(activeItem), noChange = activeItem === oldActiveItem && isActive2;
    if (!noChange) {
      if (isActive2) {
        me.previousActiveItem = oldActiveItem;
      }
      if (oldActiveItem) {
        oldActiveItem.classList.remove(me.focusCls);
        oldActiveItem.removeAttribute("id");
      }
    }
    if (activeItem != null) {
      if (me.skipScrollIntoView) {
        if (needsFocus) {
          activeItem.focus({ preventScroll: true });
        }
      } else {
        (_a5 = ownerCmp.scrollable) == null ? void 0 : _a5.scrollIntoView(activeItem, {
          block: "nearest",
          focus: needsFocus,
          silent: me.scrollSilently
        });
      }
      if (noChange) {
        return;
      }
      if (!activeItem.classList.contains(me.focusCls)) {
        activeItem.classList.add(me.focusCls);
      }
      activeItem.id = `${me.owner.id}-active-descendant`;
      me._activeItem = activeItem;
    } else {
      me._activeItem = null;
      if (oldActiveItem === DomHelper.getActiveElement(oldActiveItem)) {
        ownerCmp.revertFocus();
      }
    }
  }
  updateTarget(target, oldTarget) {
    const me = this, listeners = {
      element: target,
      thisObj: me,
      focusin: "onTargetFocusIn",
      focusout: "onTargetFocusOut",
      // If items are tabbable the mousedown handler will not force the issue by focusing
      // the closest item. We just need to trigger the method so that it is hookable
      mousedown: {
        handler: "onTargetMouseDown",
        delegate: me.itemSelector
      }
    };
    if (!me.itemsTabbable && !me.itemsFocusable) {
      listeners.click = "onTargetClick";
    }
    if (me.activateOnMouseover) {
      listeners.mouseover = {
        handler: "onTargetMouseover",
        delegate: me.itemSelector,
        delay: 30
      };
    }
    if (!Object.prototype.hasOwnProperty.call(me, "acceptNode")) {
      me.acceptNode = me.acceptNode.bind(me);
    }
    EventHelper.on(listeners);
    me.treeWalker = me.setupTreeWalker(target, DomHelper.NodeFilter.SHOW_ELEMENT, me.acceptNode);
    if (!me.keyEventTarget) {
      me.keyEventTarget = target;
    }
    (me.targetMutationMonitor = new MutationObserver(me.onTargetChildListChange.bind(me))).observe(target, {
      childList: true,
      subtree: true
    });
  }
  setupTreeWalker(root, whatToShow, filter2) {
    return document.createTreeWalker(root, whatToShow, filter2);
  }
  onTargetChildListChange() {
    var _a5;
    const me = this, {
      activeItem,
      datasetIdProperty
    } = me;
    if (activeItem) {
      if (me.target.contains(activeItem)) {
        if (!activeItem.classList.contains(me.focusCls)) {
          activeItem.classList.add(me.focusCls);
        }
      } else {
        me.activeItem = me.target.querySelector(`${me.itemSelector}.${me.focusCls}[data-${StringHelper.hyphenate(datasetIdProperty)}="${activeItem.dataset[datasetIdProperty]}"]`) || ((_a5 = me.target.querySelector(`${me.itemSelector}.${me.focusCls}`)) == null ? void 0 : _a5[activeItem.dataset.index]) || null;
      }
    }
  }
  updateKeyEventTarget(keyEventTarget) {
    var _a5;
    (_a5 = this.keyListenerRemover) == null ? void 0 : _a5.call(this);
    this.keyListenerRemover = EventHelper.on({
      element: keyEventTarget,
      keydown: "onKeyDown",
      thisObj: this
    });
  }
  triggerNavigate(event, item = this.activeItem) {
    var _a5;
    const navEvent = {
      event,
      item,
      oldItem: this.previousActiveItem
    };
    this.trigger("navigate", navEvent);
    (_a5 = this.ownerCmp) == null ? void 0 : _a5.trigger("navigate", navEvent);
  }
};
__publicField(Navigator, "$name", "Navigator");
// Factoryable type name
__publicField(Navigator, "type", "navigator");
Navigator._$name = "Navigator";

// ../Core/lib/Core/mixin/Clipboardable.js
var Clipboard = class extends Base.mixin(Events_default) {
  constructor() {
    super(...arguments);
    // Defaults to true, so to set this lazy on first read/write
    __publicField(this, "hasNativeAccess", true);
    __publicField(this, "_content", null);
  }
  /**
   * Write to the native Clipboard API or a local clipboard as a fallback.
   * @param {String} text Only allows string values
   * @param {Boolean} allowNative `true` will try writing to the Clipboard API once
   * @private
   */
  async writeText(text, allowNative) {
    const me = this, { _content } = me;
    if (allowNative && me.hasNativeAccess) {
      try {
        await navigator.clipboard.writeText(text);
      } catch (e) {
        me.hasNativeAccess = false;
      }
    }
    if (_content !== text) {
      me._content = text;
      me.triggerContentChange(_content, false, true);
    }
  }
  /**
   * Writes any data to the local shared clipboard.
   * @param {Object} data
   * @param {String} source source name
   * @private
   */
  writeData(data, source) {
    this._dataContent = data;
    this._contentSource = source;
  }
  /**
   * Reads from the native Clipboard API or a local clipboard as a fallback.
   * @param {Boolean} allowNative `true` will try reading from the Clipboard API once
   * @private
   */
  async readText(allowNative) {
    const me = this, { _content } = me;
    if (allowNative && me.hasNativeAccess) {
      try {
        const text = await navigator.clipboard.readText();
        if (_content !== text) {
          me._content = text;
          me._dataContent = me._contentSource = null;
          me.triggerContentChange(_content, true);
        }
        return text;
      } catch (e) {
        me.hasNativeAccess = false;
      }
    }
    return _content;
  }
  /**
   * Reads any data from the local shared clipboard.
   * @param {Array<String>} allowedSources Array of source names which is supported
   * @private
   */
  readData(allowedSources) {
    if (allowedSources && !ArrayHelper.asArray(allowedSources).includes(this._contentSource)) {
      return;
    }
    return this._dataContent;
  }
  /**
   * Call this to let other instances know that data has been pasted
   * @param {Object} source
   */
  triggerPaste(source) {
    this.trigger("paste", { source, text: this._content });
  }
  triggerContentChange(oldText, fromRead = false, fromWrite = false) {
    this.trigger("contentChange", { fromRead, fromWrite, oldText, newText: this._content });
  }
  async clear(allowNative) {
    await this.writeText("", allowNative);
    this.writeData(null);
  }
};
var Clipboardable_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    construct(...args) {
      super.construct(...args);
      if (!globalThis.bryntum.clipboard) {
        globalThis.bryntum.clipboard = new Clipboard();
      }
      globalThis.bryntum.clipboard.ion({
        paste: "onClipboardPaste",
        contentChange: "onClipboardContentChange",
        thisObj: this
      });
    }
    /**
     * Gets the current shared Clipboard instance
     * @private
     */
    get clipboard() {
      return globalThis.bryntum.clipboard;
    }
    // Called when someone triggers a paste event on the shared Clipboard
    onClipboardPaste({ text, source }) {
      var _a6;
      const me = this, { clipboardText, isCut } = me, isOwn = me.compareClipboardText(clipboardText, text);
      if (isOwn && isCut) {
        (_a6 = me.handleCutData) == null ? void 0 : _a6.call(me, { text, source });
        me.isCut = false;
        me.cutData = null;
      } else if (!isOwn) {
        me.clearClipboard(false);
      }
    }
    // Calls when the shared clipboard writes or reads a new string value
    onClipboardContentChange({ newText }) {
      if (!this.compareClipboardText(this.clipboardText, newText)) {
        this.clearClipboard(false);
      }
    }
    // When a cut is done, or a cut is deactivated
    set cutData(data) {
      var _a6, _b2;
      const me = this;
      (_a6 = me._cutData) == null ? void 0 : _a6.forEach((r) => me.setIsCut(r, false));
      me._cutData = ArrayHelper.asArray(data);
      (_b2 = me._cutData) == null ? void 0 : _b2.forEach((r) => me.setIsCut(r, true));
    }
    get cutData() {
      return this._cutData;
    }
    /**
     * Writes string data to the shared/native clipboard. Also saves a local copy of the string and the unconverted
     * data.
     *
     * But firstly, it will call beforeCopy function and wait for a response. If false, the copy will be prevented.
     *
     * @param {Object} data
     * @param {Boolean} isCut
     * @param {Object} [params] Will be passed to beforeCopy function
     * @returns {Object} Object containing the original data, cloned data and the string that was written to the
     * clipboard
     * @private
     */
    async writeToClipboard(data, isCut, params = {}) {
      if (await this.beforeCopy({ data, isCut, ...params }) === false) {
        return;
      }
      const me = this, isString = typeof data === "string", stringData = isString ? data : me.stringConverter ? me.stringConverter(data) : StringHelper.safeJsonStringify(data), clonedData = isString ? stringData : me.cloneData ? me.cloneData({ data, stringData, isPaste: false }) : ObjectHelper.clone(data);
      me.clipboardText = stringData;
      me.clipboard.writeData(clonedData, me.$$name);
      await me.clipboard.writeText(stringData, me.useNativeClipboard);
      ;
      me.clipboardData = clonedData;
      me.originalData = data;
      me.isCut = isCut;
      me.cutData = isCut && !isString ? data : null;
      return {
        data: isCut ? data : clonedData,
        clonedData,
        originalData: data,
        stringData
      };
    }
    /**
     * Reads string data from the shared/native clipboard. If string matches current instance local clipboard data, a
     * non-modified version will be return. Otherwise, a stringParser function will be called.
     *
     * But firstly, it will call beforePaste function and wait for a response. If false, the paste will be prevented.
     *
     * This function will also trigger a paste event on the clipboard instance.
     *
     * @param {Object} [params] Will be passed to beforePaste function
     * @param {Boolean} [skipPasteTrigger] Set to `true` not trigger a paste when paste completes
     * @returns {Object}
     * @private
     */
    async readFromClipboard(params = {}, skipPasteTrigger = false) {
      var _a6;
      const me = this, { clipboard } = me, text = await clipboard.readText(me.useNativeClipboard), isOwn = me.compareClipboardText(me.clipboardText, text), originalData = isOwn ? me.originalData : [];
      let data = clipboard.readData(me.allowedDataSources);
      if (!isOwn) {
        me.clearClipboard(false);
      }
      const { isCut } = me;
      if (isCut || !data || data === text) {
        data = me.transformClipboardText(text);
      }
      if (data && !isCut && typeof data !== "string" && me.cloneData) {
        data = me.cloneData({ data, isPaste: true });
      }
      if (data == null || Array.isArray(data) && data.length === 0 || // Hook to trigger event or something like that
      await ((_a6 = me.beforePaste) == null ? void 0 : _a6.call(me, { data, text, originalData, ...params, isCut, isOwn })) === false) {
        return;
      }
      skipPasteTrigger || clipboard.triggerPaste(me);
      return { data, text, isOwn, isCut, originalData };
    }
    /**
     * Clears the clipboard data
     * @privateparam {Boolean} [clearShared] Set to `false` not to clear the internally shared and native clipboard
     * @category Common
     */
    async clearClipboard(clearShared = true) {
      const me = this;
      me.clipboardData = me.originalData = me.clipboardText = me.cutData = null;
      me.isCut = false;
      if (clearShared) {
        await me.clipboard.clear(me.useNativeClipboard);
      }
    }
    compareClipboardText(a, b) {
      const regex = /\r\n|(?!\r\n)[\n-\r\x85\u2028\u2029]/g;
      return (a == null ? void 0 : a.replace(regex, "\n")) === (b == null ? void 0 : b.replace(regex, "\n"));
    }
    /**
     * Takes a clipboard text and returns the parsed `data`
     * @param {String} text The text string that was read from the clipboard
     * @returns {Object}
     * @private
     */
    transformClipboardText(text) {
      const me = this;
      return me.compareClipboardText(me.clipboardText, text) ? me.isCut ? me.originalData : me.clipboardData : me.stringParser && text ? me.stringParser(text) : text;
    }
    /**
     * Checks local clipboard if there is clipboard data present. If native clipboard API is available, this function
     * will return `undefined`
     * @returns {Object}
     * @private
     */
    hasClipboardData() {
      const { clipboard } = this, { _content } = clipboard;
      if (this.useNativeClipboard && clipboard.hasNativeAccess) {
        return;
      }
      return Boolean(_content && this.transformClipboardText(_content));
    }
  }, __publicField(_a5, "$name", "Clipboardable"), __publicField(_a5, "configurable", {
    /**
     * Set this to `true` to use native Clipboard API if it is available
     * @config {Boolean}
     * @default
     * @private
     */
    useNativeClipboard: false,
    /**
     * Configure which sources $$name this class allows pasting model data from. Accepts string or array.
     * Unspecified accepts all. If source is not accepted, it will try to use the string value instead.
     * @config {String|Array<String>}
     * @private
     */
    allowedDataSources: null
  }), _a5;
};

// ../Core/lib/Core/mixin/Finalizable.js
var Finalizable_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    construct(...args) {
      super.construct(...args);
      this.finalizer = null;
      this.finalizing = null;
      this.isFinalized = false;
      this.isFinalizing = false;
    }
    /**
     * This template method is called at the end of {@link #function-finalize}. By default it calls `destroy()`, but
     * can be replaced by the derived class. This can be useful if it is not the `Finalizable` instance that awaits
     * the {@link #function-finalize} method.
     */
    doFinalize() {
      this.destroy();
    }
    /**
     * This method is called (typically by this instance or its owner) to cleanup this instance while possibly first
     * waiting for the {@link #property-finalizer} promise to settle. Once settled, the {@link #function-doFinalize}
     * method is called.
     * @async
     */
    finalize() {
      const me = this;
      let ret = me.finalizing;
      if (!ret && !me.isFinalized) {
        me.isFinalizing = true;
        me.finalizing = ret = me._awaitFinalizer();
      }
      return ret;
    }
    async _awaitFinalizer() {
      const me = this;
      try {
        await me.finalizer;
      } finally {
        me.finalizing = null;
        me.isFinalized = true;
        me.doFinalize();
      }
    }
  }, __publicField(_a5, "$name", "Finalizable"), _a5;
};

// ../Core/lib/Core/util/drag/DragContext.js
var ABORTED = Symbol("dragAbort");
var INIT = Symbol("dragInit");
var DRAGGING = Symbol("dragDrag");
var DROPPED = Symbol("dragDrop");
var lockDirections = {
  x: "horizontal",
  y: "vertical"
};
var DragContext = class extends Base.mixin(Finalizable_default, Delayable_default, Identifiable_default) {
  static get configurable() {
    return {
      /**
       * The element that will have the {@link Core.mixin.Draggable#property-draggingItemCls}. This element is
       * determined by the {@link Core.mixin.Draggable#config-dragItemSelector}.
       * @config {HTMLElement}
       */
      itemElement: null,
      /**
       * The `ScrollManager` instance to use for scrolling while dragging.
       * @config {Core.util.ScrollManager}
       * @private
       */
      scrollManager: null,
      /**
       * Config for `startMonitoring` call.
       * @config {Object}
       * @private
       */
      monitoringConfig: null,
      /**
       * The source of the drag operation.
       * @config {Core.mixin.Draggable}
       * @default
       * @readonly
       * @private
       */
      source: null,
      /**
       * The current target of the drag.
       * @member {Core.mixin.Droppable}
       * @readonly
       * @private
       */
      target: null,
      /**
       * The current target element of the drag.
       * @member {HTMLElement}
       * @private
       */
      targetElement: null,
      /**
       * The minimum distance from the touchstart/mousedown/pointerdown that must be moved to actually start a
       * drag operation.
       * @config {Number}
       * @default
       * @readonly
       */
      threshold: 5,
      /**
       * The minimum amount of time a touch must be maintained before it will initiate a drag. Movement prior to
       * this time will cancel the drag in order to allow touch scrolling.
       * @config {Number}
       * @default
       */
      touchStartDelay: 300,
      // To pass through the config system when set, so we can flag it to not be unfixed
      event: null
    };
  }
  static get identifiable() {
    return {};
  }
  /**
   * The current DOM event being processed.
   * @member {Event} event
   * @readonly
   */
  construct(...args) {
    super.construct(...args);
    const me = this, { event } = me;
    Object.assign(me, {
      /**
       * This property holds the `altKey` state of the most recent event.
       * @member {Boolean}
       */
      altKey: null,
      /**
       * An array of functions to call when cleaning up the context instance.
       * @member {Function[]}
       * @private
       */
      cleaners: [],
      /**
       * This property holds the `ctrlKey` state of the most recent event.
       * @member {Boolean}
       */
      ctrlKey: null,
      /**
       * Container for data associated with the drag. Data items are added by the {@link Core.mixin.Draggable}
       * when the drag starts.
       * @member {Map}
       * @private
       */
      data: /* @__PURE__ */ new Map(),
      /**
       * The element from which the drag operation started.
       * @member {HTMLElement}
       * @readonly
       */
      element: event.target,
      /**
       * The event that completed the drag (a `mouseup`, `pointerup` or `touchend`).
       * @member {Event}
       * @readonly
       */
      endEvent: null,
      /**
       * The most recent `mousemove`, `pointermove` or `touchmove` event.
       * @member {Event}
       * @private
       */
      lastMoveEvent: null,
      /**
       * This property holds the `metaKey` state of the most recent event.
       * @member {Boolean}
       */
      metaKey: null,
      /**
       * The previous {@link #property-target} of the drag.
       * @member {Core.mixin.Droppable}
       * @readonly
       * @private
       */
      previousTarget: null,
      /**
       * The scroll actions reported by the {@link #config-scrollManager}.
       * @member {Object}
       * @private
       */
      scrollerAction: null,
      /**
       * This property holds the `shiftKey` state of the most recent event.
       * @member {Boolean}
       */
      shiftKey: null,
      /**
       * This property holds the current state of the drag process.
       *
       * This will be one of the following values:
       *
       *  - `DragContext.STATE.INIT` - The button is down but there is insufficient movement to start the drag.
       *  - `DragContext.STATE.DRAGGING` - The button is down and movement has started the drag.
       *  - `DragContext.STATE.DROPPED` - The button has been released and drop has occurred.
       *  - `DragContext.STATE.ABORTED` - The drag has been aborted (this happens if the user presses the `ESC`
       *    key or if the {@link #function-abort} method is called).
       *
       * @member {Symbol}
       * @readonly
       * @internal
       */
      state: INIT,
      /**
       * The event that started the drag operation.
       * @member {Event}
       * @readonly
       */
      startEvent: event,
      /**
       * The timer that fires when a touch pointermove is allowed to start the drag. A touch pointermove event
       * prior to this will `abort()` the drag to allow touch scrolling.
       * @member {Number}
       * @private
       */
      touchStartTimer: null,
      /**
       * Stores the value from writes to the {@link #property-valid} property.
       * @member {Boolean}
       * @private
       */
      _valid: true
    });
    if ("touches" in event && me.touchStartDelay) {
      me.touchStartTimer = me.setTimeout(() => me.touchStartTimer = null, me.touchStartDelay, "touchStartDelay");
    }
    const element = globalThis;
    EventHelper.on({
      element,
      blur: "onWindowBlur",
      thisObj: me,
      autoDetach: true
    });
  }
  doDestroy() {
    const me = this, { source, target } = me;
    me.cleanup();
    if ((target == null ? void 0 : target.dropping) === me) {
      target.dropping = null;
    }
    if ((source == null ? void 0 : source.dragging) === me) {
      source.dragging = null;
    }
    super.doDestroy();
  }
  onWindowBlur() {
    if (this.started && !this.completed) {
      this.abort();
    }
  }
  /**
   * This property is `true` if the {@link #function-abort} method was called and `false` otherwise. This
   * is typically because the user pressed the ESC key, however, a drag can be aborted for other reasons.
   * @property {Boolean}
   * @readonly
   */
  get aborted() {
    return this.state === ABORTED;
  }
  /**
   * Returns `true` if the drag has completed either by mouse/pointerup or the {@link #function-abort} method.
   * @property {Boolean}
   * @readonly
   */
  get completed() {
    return this.isDestroying || this.aborted || this.endEvent !== null;
  }
  /**
   * This property is `true` if the drag {@link #config-threshold} has not yet been reached.
   * @property {Boolean}
   * @readonly
   */
  get pending() {
    return this.state === INIT;
  }
  /**
   * This property is `true` if the drag {@link #config-threshold} has been reached and the drag operation is active.
   * @property {Boolean}
   * @readonly
   */
  get started() {
    return this.state !== INIT && !this.aborted;
  }
  /**
   * This property is `true` when the drag is in a valid drop state. This can be set to `false` to indicate the drop
   * is invalid. Setting to `true` does not ensure that the property will be `true` when next read due to other factors
   * that are required to make the drop valid. For example, setting `valid = true` will still return `false` if called
   * before the drag {@link #config-threshold} has not been reached or if the {@link #function-abort} method has been
   * called.
   * @property {Boolean}
   */
  get valid() {
    return this.started && this.targetElement != null && this._valid;
  }
  set valid(v) {
    this._valid = v;
  }
  //region Data Access
  /**
   * Retrieves a data item from the drag source. This method can only be called after the drag has completed.
   * @param {String|String[]} name The name of the data item.
   * @returns {*}
   */
  async get(name) {
    if (this.aborted) {
      throw new Error("Data is not available on aborted drag");
    }
    if (!this.completed) {
      throw new Error("Data is not available until drag completion");
    }
    if (Array.isArray(name)) {
      return Promise.all(name.map((s) => this.get(s)));
    }
    let value2 = this.data.get(name);
    if (typeof value2 === "function") {
      value2 = await value2();
      this.data.set(name, value2);
    }
    return value2;
  }
  /**
   * Returns `true` if the named data item is present.
   * @param {String} name The name of the data item.
   * @returns {Boolean}
   */
  has(name) {
    return this.data.has(name);
  }
  /**
   * Retrieves a data item from the drag source if it is available. This will return `true` for an item that was
   * {@link #function-set} using a renderer function.
   * @param {String|String[]} name The name of the data item.
   * @returns {*}
   */
  peek(name) {
    if (this.aborted) {
      throw new Error("Data is not available on aborted drag");
    }
    if (Array.isArray(name)) {
      return name.map((s) => this.peek(s));
    }
    let value2 = this.data.get(name);
    if (typeof value2 === "function") {
      value2 = true;
    }
    return value2;
  }
  /**
   * Sets a data item for the drag. If a function is passed, it is called to render the data only if that data is
   * actually requested via the {@link #function-get} method. A data renderer function can be `async`.
   * @param {String} name The name of the data item.
   * @param {*} value The value of the data item.
   */
  set(name, value2) {
    this.data.set(name, value2);
  }
  //endregion
  //region Configs
  changeTarget(target, was) {
    if (target !== was) {
      const me = this;
      me._target = target;
      me.previousTarget = was;
      if (was) {
        was.dropping = null;
      }
      if (target) {
        target.dropping = me;
        if (target.dropping !== me) {
          target = null;
          me.valid = false;
        }
      }
      me._target = was;
    }
    return target;
  }
  updateTarget(target, was) {
    const me = this;
    if (was) {
      me.source.dragLeaveTarget(me, was);
    }
    if (target) {
      me.valid = true;
      target.dragMove(me);
      me.source.dragEnterTarget(me);
    }
  }
  updateTargetElement(targetElement) {
    let droppable, droppables, droppableSelector, i, t;
    for (t = targetElement; t; t = t.parentElement) {
      droppables = DomDataStore.get(t, "droppables");
      if (droppables) {
        for (i = 0; i < droppables.length; ++i) {
          droppable = droppables[i];
          if (droppable.dropRootElement.contains(targetElement)) {
            droppableSelector = droppable.droppableSelector;
            if (!droppableSelector || targetElement.closest(`#${DomHelper.getId(droppable.dropRootElement)} ${droppableSelector}`)) {
              this.target = droppable;
              if (this.target === droppable) {
                return;
              }
            }
          }
        }
      }
    }
  }
  //endregion
  //region Operations
  /**
   * Aborts the drag. After calling this method, {@link #property-aborted} will be `true`, {@link #property-valid}
   * will be `false` and {@link #property-completed} will be `true`.
   */
  abort() {
    const me = this, { element, source } = me;
    element == null ? void 0 : element.getBoundingClientRect();
    if (me.state !== DROPPED) {
      me.state = ABORTED;
      me.cleanup();
    }
    source == null ? void 0 : source.endDrag(me);
  }
  begin() {
    const me = this, { source } = me, ret = source.beforeDrag(me);
    if (ret !== false) {
      source.dragging = me;
    }
    return ret;
  }
  cleanup() {
    let cleaner;
    while (cleaner = this.cleaners.pop()) {
      cleaner();
    }
  }
  end(event) {
    const me = this, { lastMoveEvent: lastEvent, target } = me, { dragSwallowClickTime } = me.source;
    me.event = me.domEvent = me.endEvent = event;
    me.syncFlags();
    if (me.started) {
      if ((lastEvent == null ? void 0 : lastEvent.clientX) !== event.clientX || (lastEvent == null ? void 0 : lastEvent.clientY) !== event.clientY || (lastEvent == null ? void 0 : lastEvent.target) !== event.target) {
        me.track();
      }
      if (dragSwallowClickTime) {
        const element = globalThis;
        EventHelper.on({
          element,
          capture: true,
          expires: dragSwallowClickTime,
          // In case a click did not ensue, remove the listener
          once: true,
          click(event2) {
            event2.stopPropagation();
          }
        });
      }
      me.state = DROPPED;
      if (target !== me.source) {
        target == null ? void 0 : target.dragDrop(me);
      }
    }
  }
  fakeKey(event, down) {
    const me = this, { lastMoveEvent } = me;
    if (lastMoveEvent && me.element) {
      let changed;
      lastMoveEvent.isKey = true;
      if (event.key === "Alt") {
        if (me.altKey !== down) {
          me.altKey = down;
          changed = true;
        }
      } else if (event.key === "Control") {
        if (me.ctrlKey !== down) {
          me.ctrlKey = down;
          changed = true;
        }
      }
      if (changed) {
        me.event = me.domEvent = lastMoveEvent;
        me.track();
      }
    }
  }
  keyDown(event) {
    if (!this.completed) {
      if (event.key === "Escape") {
        this.abort();
      } else if (this.isDragToggleKey(event.key)) {
        this.fakeKey(event, true);
      }
    }
  }
  keyUp(event) {
    if (!this.completed && this.isDragToggleKey(event.key)) {
      this.fakeKey(event, false);
    }
  }
  getDistance(event) {
    return EventHelper.getDistanceBetween(this.startEvent, event);
  }
  isDragToggleKey(key) {
    return key === "Control" || key === "Alt";
  }
  move(event) {
    const me = this, { target } = event, distance = me.getDistance(event), significant = distance >= me.threshold;
    me.syncFlags();
    if (me.touchStartTimer) {
      if (significant) {
        me.abort();
      }
      return;
    }
    if (target && target.nodeType === Node.ELEMENT_NODE && target.isConnected) {
      if (significant && !me.started) {
        me.event = me.domEvent = event;
        if (me.start() === false) {
          me.abort();
          return;
        }
      }
      if (event.touches) {
        me.startEvent.preventDefault();
        event.preventDefault();
        event.stopPropagation();
      }
      if (me.started && !me.completed) {
        me.lastMoveEvent = me.event = me.domEvent = event;
        if (event.type === "touchmove") {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
        me.track();
      }
    }
  }
  start() {
    const me = this, { scrollManager, monitoringConfig, source } = me, { draggingBodyCls: activeCls, dragLock } = source, element = (
      /* source.dragWithin || */
      source.dragRootElement
    );
    me.state = DRAGGING;
    if (scrollManager) {
      const detacher2 = scrollManager.startMonitoring(Objects.merge({
        scrollables: [{
          element
        }],
        direction: lockDirections[dragLock] || dragLock || "both",
        callback(scrollerAction) {
          const { lastMoveEvent } = me;
          if (lastMoveEvent && me.element) {
            lastMoveEvent.isScroll = true;
            me.event = me.domEvent = lastMoveEvent;
            me.scrollerAction = scrollerAction;
            me.track();
            me.scrollerAction = null;
          }
        }
      }, monitoringConfig));
      me.cleaners.push(detacher2);
    }
    const rootEl = source.dragRootElement.closest(".b-outer") || document.body;
    rootEl.classList.add(activeCls);
    me.cleaners.push(() => rootEl.classList.remove(activeCls));
    if (source.startDrag(me) === false) {
      me.cleanup();
      return false;
    }
  }
  syncFlags() {
    const me = this, { event } = me;
    me.altKey = event.altKey;
    me.ctrlKey = event.ctrlKey || event.metaKey;
    me.metaKey = event.metaKey;
    me.shiftKey = event.shiftKey;
  }
  track() {
    const me = this, { event, source, target } = me;
    let targetElement = event.target, touch;
    if (event.type === "touchmove") {
      touch = event.changedTouches[0];
      targetElement = DomHelper.elementFromPoint(touch.clientX, touch.clientY);
    }
    me.targetElement = targetElement;
    if (target === me.target) {
      target == null ? void 0 : target.dragMove(me);
    }
    source.trackDrag(me);
  }
  //endregion
};
__publicField(DragContext, "$name", "DragContext");
DragContext.prototype.STATE = DragContext.STATE = Object.freeze({
  ABORTED,
  INIT,
  DRAGGING,
  DROPPED
});
DragContext._$name = "DragContext";

// ../Core/lib/Core/util/drag/DragProxy.js
var _DragProxy = class _DragProxy extends Base.mixin(Factoryable_default) {
  static get configurable() {
    return {
      /**
       * The currently active `DragContext`. This context will be active prior to be passed to the proxy. This
       * config is set by {@link #function-dragStart} and cleared by {@link #function-dragEnd}.
       * @config {Core.util.drag.DragContext}
       */
      dragging: null
    };
  }
  static get factoryable() {
    return {
      defaultType: _DragProxy
    };
  }
  /**
   * The `Draggable` instance that owns this drag proxy.
   * @member {Core.mixin.Draggable} owner
   * @readonly
   */
  //region Configs
  updateDragging(drag, was) {
    if (was) {
      this.close(was);
    }
    if (drag) {
      this.open(drag);
    }
  }
  //endregion
  //region Operations
  /**
   * This template method is called when {@link #config-dragging} is reset to `null`.
   * @param {Core.util.drag.DragContext} drag The drag instance.
   */
  close(drag) {
  }
  /**
   * This template method is called when {@link #config-dragging} is set to a non-`null` value.
   * @param {Core.util.drag.DragContext} drag The drag instance.
   */
  open(drag) {
  }
  //endregion
  //region Drag Processing
  /**
   * This template method is called by the `Draggable` instance when the drag officially starts.
   * This sets the {@link #config-dragging} config to `drag`, which triggers the call to {@link #function-open}.
   * @param {Core.util.drag.DragContext} drag The drag instance.
   */
  dragStart(drag) {
    this.dragging = drag;
  }
  /**
   * This template method is called by the `Draggable` instance as drag movement occurs.
   * @param {Core.util.drag.DragContext} drag The drag instance.
   */
  dragMove(drag) {
  }
  /**
   * This template method is called by the `Draggable` instance when the drag completes.
   *
   * This sets the {@link #config-dragging} config to `null`, which triggers the call to {@link #function-close}.
   * @param {Core.util.drag.DragContext} drag The drag instance.
   */
  dragEnd(drag) {
    this.dragging = null;
  }
  //endregion
};
__publicField(_DragProxy, "type", "default");
var DragProxy = _DragProxy;
DragProxy.initClass();
DragProxy._$name = "DragProxy";

// ../Core/lib/Core/mixin/Draggable.js
var Draggable_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    //endregion
    //region Drag Processing
    // These template methods are implemented by derived classes as desired. There is only one overlap with Droppable's
    // template methods (dragDrop) so that a class can easily mixin both Draggable and Droppable and always distinguish
    // whether it is acting as the source, the target, or both.
    /**
     * This template method is called when the mousedown of a potential drag operation occurs. This happens before the
     * gesture is known to be a drag, meaning the {@link #config-dragThreshold} has not been reached. This method
     * should initialize the {@link Core.util.drag.DragContext} using the {@link Core.util.drag.DragContext#function-set}
     * method. Alternatively, this method may return `false` to prevent the drag operation.
     *
     * *Important:* Because no drag has occurred at the time this method is called, only minimal processing should be
     * done (such as initializing the {@link Core.util.drag.DragContext}). Anything more should be done in the
     * {@link #function-dragStart} method or in response to the {@link #event-dragStart} event which happen only if
     * the user drags the mouse before releasing the mouse button.
     * @param {Core.util.drag.DragContext} drag
     */
    beforeDrag(drag) {
      const { dragRootElement, dragSelector, ignoreSelector } = this, target = dragSelector && drag.element.closest(dragSelector);
      return !dragSelector || Boolean(
        target && target === dragRootElement || dragRootElement.contains(target) && (!ignoreSelector || !drag.element.matches(ignoreSelector))
      );
    }
    /**
     * This template method is called when the drag operation starts. This occurs when the {@link #config-dragThreshold}
     * has been reached.
     * Your implementation may return `false` to prevent the startup of the drag operation.
     * @param {Core.util.drag.DragContext} drag
     */
    dragStart(drag) {
    }
    /**
     * This template method is called as the drag moves. This occurs on each mouse/pointer/touchmove event.
     * @param {Core.util.drag.DragContext} drag
     */
    dragOver(drag) {
    }
    /**
     * This template method is called when the drag enters a {@link Core.mixin.Droppable target}.
     * @param {Core.util.drag.DragContext} drag
     */
    dragEnterTarget(drag) {
    }
    /**
     * This template method is called when the drag leaves a {@link Core.mixin.Droppable target}.
     * @param {Core.util.drag.DragContext} drag
     * @param {Core.mixin.Droppable} oldTarget The previous value of `drag.target`.
     */
    dragLeaveTarget(drag, oldTarget) {
    }
    /**
     * This template method is called when the drag operation completes. This occurs on the pointerup event.
     *
     * This method is not called if the drag is {@link Core.util.drag.DragContext#property-aborted}.
     * @param {Core.util.drag.DragContext} drag
     */
    async dragDrop(drag) {
    }
    /**
     * This template method is called when the drag operation completes. This occurs on the pointerup event or perhaps
     * a keypress event.
     *
     * This method is always called, even if the drag is {@link Core.util.drag.DragContext#property-aborted}.
     * @param {Core.util.drag.DragContext} drag
     */
    dragEnd(drag) {
    }
    //endregion
    //region Drag Management
    // These methods are called by the DragContext and generally manage element updates (adding/removing classes) or
    // event firing. In most cases these methods then call a corresponding Drag Processing template method intended
    // for derived classes to implement.
    get activeDrag() {
      const { dragging: drag } = this;
      return (drag == null ? void 0 : drag.started) && !drag.completed ? drag : null;
    }
    /**
     * Return the `Events` instance from which drag events are fired.
     * @internal
     * @property {Core.mixin.Events}
     */
    get dragEventer() {
      return this.trigger ? this : null;
    }
    get draggingClassElement() {
      const { draggingClsSelector, dragRootElement } = this;
      return draggingClsSelector ? dragRootElement == null ? void 0 : dragRootElement.closest(draggingClsSelector) : dragRootElement;
    }
    beginDrag(drag) {
      const { draggingCls, draggingClassElement } = this;
      if (draggingCls && draggingClassElement) {
        draggingClassElement.classList.add(draggingCls);
        drag.cleaners.push(() => draggingClassElement.classList.remove(draggingCls));
      }
    }
    async endDrag(drag) {
      const me = this, { dragEventer, dragProxy } = me;
      if (drag.valid) {
        await me.dragDrop(drag);
      }
      if (me.isDestroyed) {
        return;
      }
      if (drag.pending) {
        drag.destroy();
      } else {
        me.dragEnd(drag);
        dragProxy == null ? void 0 : dragProxy.dragEnd(drag);
        dragEventer == null ? void 0 : dragEventer.trigger(drag.valid ? "drop" : "dragCancel", { drag, event: drag.event });
        me.finalizeDrag(drag);
      }
    }
    async finalizeDrag(drag) {
      var _a6;
      await ((_a6 = drag.finalize) == null ? void 0 : _a6.call(drag));
    }
    moveDrag(drag) {
      if (this.dragOver(drag) !== false) {
        const { dragEventer, dragProxy } = this;
        dragProxy == null ? void 0 : dragProxy.dragMove(drag);
        dragEventer == null ? void 0 : dragEventer.trigger("drag", { drag, event: drag.event });
      }
    }
    setupDragContext(event) {
      const me = this, { dragItemSelector, id: id2 } = me, { target } = event;
      return {
        event,
        id: id2 ? `${id2}-drag-${me._nextDragId = (me._nextDragId || 0) + 1}` : null,
        itemElement: dragItemSelector ? target.closest(dragItemSelector) : target,
        touchStartDelay: me.dragTouchStartDelay,
        source: me,
        threshold: me.dragThreshold
      };
    }
    startDrag(drag) {
      const { draggingStartedCls, draggingClassElement, draggingItemCls, dragEventer, dragProxy } = this, { itemElement } = drag;
      if ((dragEventer == null ? void 0 : dragEventer.trigger("beforeDragStart", { drag, event: drag.event })) === false) {
        return false;
      }
      if (draggingStartedCls && draggingClassElement) {
        draggingClassElement.classList.add(draggingStartedCls);
        drag.cleaners.push(() => draggingClassElement.classList.remove(draggingStartedCls));
      }
      if (draggingItemCls && itemElement) {
        itemElement.classList.add(draggingItemCls);
        drag.cleaners.push(() => itemElement.classList.remove(draggingItemCls));
      }
      dragProxy == null ? void 0 : dragProxy.dragStart(drag);
      const result = this.dragStart(drag);
      if (result !== false) {
        dragEventer == null ? void 0 : dragEventer.trigger("dragStart", { drag, event: drag.event });
      }
      return result;
    }
    trackDrag(drag) {
      var _a6;
      const { dropTargetSelector } = this;
      drag.valid = !(dropTargetSelector && !((_a6 = drag.targetElement) == null ? void 0 : _a6.closest(dropTargetSelector)));
      this.moveDrag(drag);
    }
    //endregion
    //region Configs
    configureListeners(drag) {
      const me = this, listeners = ObjectHelper.assign({
        thisObj: me
      }, me.dragDocumentListeners);
      if ("touches" in drag.startEvent) {
        delete listeners.mousemove;
        delete listeners.mouseup;
      } else {
        delete listeners.contextmenu;
        delete listeners.touchmove;
        delete listeners.touchend;
        delete listeners.pointerup;
      }
      listeners.element = DomHelper.getRootElement(me.dragRootElement);
      return listeners;
    }
    //endregion
    //region Configs
    updateDragging(drag, old) {
      const me = this;
      if (drag) {
        const listeners = me.configureListeners(drag);
        drag.cleaners.push(EventHelper.on(listeners));
        me.beginDrag(drag);
      } else if (old) {
        old.destroy();
      }
    }
    changeDragProxy(config, existing) {
      return DragProxy.reconfigure(existing, config, {
        owner: this,
        defaults: {
          owner: this
        }
      });
    }
    updateDragRootElement(rootEl, was) {
      var _a6;
      const me = this, {
        draggableCls,
        dragItemSelector,
        onDragItemMouseMove
      } = me;
      was == null ? void 0 : was.classList.remove(draggableCls);
      (_a6 = me._dragRootDetacher) == null ? void 0 : _a6.call(me);
      if (rootEl) {
        const listeners = {
          thisObj: me,
          element: rootEl,
          mousedown: "onDragMouseDown",
          // We have touchstart listener in place since Siesta/Chrome can send these events even on non-touch
          // devices:
          touchstart: "onDragTouchStart",
          // On iOS, because we use pointerup to represent the drop gesture,
          // the initiating pointerdown event is captured, and its target is
          // the original start target. We must always release pointer capture.
          // https://github.com/bryntum/support/issues/4111
          pointerdown: (e) => {
            var _a7, _b2;
            return e.pointerId && ((_b2 = (_a7 = e.target).releasePointerCapture) == null ? void 0 : _b2.call(_a7, e.pointerId));
          }
        };
        if (onDragItemMouseMove) {
          listeners.mousemove = {
            delegate: dragItemSelector,
            handler: "onDragItemMouseMove"
          };
        }
        if (me.dragItemOverCls || onDragItemMouseMove || me.onDragItemMouseEnter || me.onDragItemMouseLeave) {
          Object.assign(listeners, {
            mouseover: {
              delegate: dragItemSelector,
              handler: "onDragItemMouseOver"
            },
            mouseout: {
              delegate: dragItemSelector,
              handler: "onDragItemMouseOut"
            }
          });
        }
        rootEl.classList.add(draggableCls);
        me._dragRootDetacher = EventHelper.on(listeners);
      }
    }
    //endregion
    //region Events
    onDragItemMouseOver(event) {
      this.overItem = event;
    }
    onDragItemMouseOut(event) {
      if (!this.dragging) {
        this.overItem = event;
      }
    }
    changeOverItem(event) {
      var _a6;
      this.enterLeaveEvent = event;
      if (event.type === "mouseout") {
        return ((_a6 = event.relatedTarget) == null ? void 0 : _a6.closest(this.dragItemSelector)) || null;
      } else {
        return event.target.closest(this.dragItemSelector);
      }
    }
    updateOverItem(overItem, oldOverItem) {
      var _a6, _b2;
      const me = this, { dragItemOverCls } = me;
      if (oldOverItem) {
        dragItemOverCls && oldOverItem.classList.remove(dragItemOverCls);
        (_a6 = me.onDragItemMouseLeave) == null ? void 0 : _a6.call(me, me.enterLeaveEvent, oldOverItem);
      }
      if (overItem) {
        dragItemOverCls && overItem.classList.add(dragItemOverCls);
        (_b2 = me.onDragItemMouseEnter) == null ? void 0 : _b2.call(me, me.enterLeaveEvent, overItem);
      }
    }
    onDragContextMenu(event) {
      event.preventDefault();
    }
    onDragKeyDown(event) {
      this.dragging.keyDown(event);
    }
    onDragKeyUp(event) {
      this.dragging.keyUp(event);
    }
    /**
     * Grab draggable element on mouse down.
     * @param {Event} event
     * @private
     */
    onDragMouseDown(event) {
      if (event.button === 0) {
        this.onDragPointerDown(event);
      }
    }
    /**
     * Grab draggable element on pointerdown.
     * @param {Event} event
     * @private
     */
    onDragPointerDown(event) {
      let { dragging: drag } = this;
      if (!drag) {
        drag = this.setupDragContext(event);
        if (drag) {
          drag = new DragContext(drag);
          if (drag.begin() === false) {
            drag.destroy();
          }
        }
      } else if (!drag.isFinalizing) {
        drag.abort();
      }
    }
    // Set by the DragContext in its begin method, and auto-nullified at destruction.
    changeDragging(value2, was) {
      was == null ? void 0 : was.destroy();
      return value2;
    }
    onDragPointerMove(event) {
      const { changedTouches } = event, { dragging: drag } = this;
      if (drag && !drag.completed && (!changedTouches || changedTouches.length === 1)) {
        drag == null ? void 0 : drag.move(event);
      }
    }
    onDragPointerUp(event) {
      const { dragging: drag } = this;
      if (drag && !drag.completed) {
        drag.end(event);
        this.endDrag(drag);
      }
    }
    /**
     * @param {Event} event
     * @private
     */
    onDragTouchStart(event) {
      if (event.touches.length === 1) {
        this.onDragPointerDown(event);
      }
    }
    //endregion
  }, __publicField(_a5, "$name", "Draggable"), //region Configs
  __publicField(_a5, "configurable", {
    /**
     * The current `DragContext`. This is created immediately on pointerdown but does not become active until
     * some movement occurs. This {@link #config-dragThreshold threshold} is configurable.
     * @member {Core.util.drag.DragContext}
     * @readonly
     */
    dragging: {
      $config: "nullify",
      value: null
    },
    /**
     * A CSS selector to use to ascend from the {@link #config-dragRootElement} to find the element that will
     * gain the {@link #property-draggingCls} and {@link #property-draggingStartedCls} CSS classes.
     * @config {String}
     */
    draggingClsSelector: null,
    /**
     * The listeners to add to the `document` during a drag.
     * @config {Object}
     * @private
     */
    dragDocumentListeners: {
      element: document,
      keydown: "onDragKeyDown",
      keyup: "onDragKeyUp",
      // On mobile, a long-press will (sometimes) trigger a context menu, so we suppress it:
      contextmenu: "onDragContextMenu",
      // We don't use pointermove/up because they get snared in the "touch-action" vs "pan-x/y" trap and we
      // cannot prevent panning (aka scrolling) in response to move events if we go that way:
      mousemove: "onDragPointerMove",
      mouseup: "onDragPointerUp",
      // Touch desktops don't fire touchend event when touch has ended, instead pointerup is fired. iOS does
      // fire touchend:
      pointerup: "onDragPointerUp",
      touchend: "onDragPointerUp",
      touchmove: {
        handler: "onDragPointerMove",
        passive: false
        // We need to be able to preventDefault on the touchmove
      }
    },
    /**
     * A CSS selector to use to ascend from the drag element to find the element that will gain the
     * {@link #property-draggingItemCls} CSS class. If not supplied, the drag element will gain this CSS
     * class.
     * @config {String}
     */
    dragItemSelector: null,
    /**
     * A CSS class to add to items identified by the {@link #config-dragItemSelector} when the mouse
     * enters.
     * @config {String}
     */
    dragItemOverCls: null,
    /**
     * A function to call when the pointer enters a {@link #config-dragItemSelector}.
     * @config {Function} onDragItemMouseEnter
     * @param {MouseEvent} event Pointer event
     * @param {HTMLElement} element Over element
     * @returns {void}
     */
    /**
     * A function to call when the pointer moves inside a {@link #config-dragItemSelector}.
     * @config {Function} onDragItemMouseMove
     * @param {MouseEvent} event Pointer event
     * @param {HTMLElement} element Over element
     * @returns {void}
     */
    /**
     * A function to call when the pointer leaves a {@link #config-dragItemSelector}.
     * @config {Function} onDragItemMouseLeave
     * @param {MouseEvent} event Pointer event
     * @param {HTMLElement} element Over element
     * @returns {void}
     */
    /**
     * Configure as `'x'` to lock dragging to the `X` axis (the drag will only move horizontally) or `'y'`
     * to lock dragging to the `Y` axis (the drag will only move vertically).
     * @config {'x'|'y'|null}
     */
    dragLock: null,
    /**
     * The minimum distance a drag must move to be considered a drop and not
     * {@link Core.util.drag.DragContext#property-aborted}.
     * @config {Number}
     * @default
     */
    dragMinDistance: 1,
    /**
     * The {@link Core.util.drag.DragProxy drag proxy} is a helper object that can be used to display feedback
     * during a drag.
     * @config {DragProxyConfig|Core.util.drag.DragProxy}
     */
    dragProxy: {
      $config: ["lazy", "nullify"],
      value: null
    },
    /**
     * The outer element where dragging will operate (attach events to it and use as root limit when looking
     * for ancestors).
     * @config {HTMLElement}
     */
    dragRootElement: {
      $config: "nullify",
      value: null
    },
    /**
     * Set to `true` to allow a drag to drop on to the same element from which the drag started.
     * @config {Boolean}
     * @default
     */
    dragSameTargetDrop: false,
    /**
     * A CSS selector used to determine which element(s) can be dragged.
     * @config {String}
     * @default
     */
    dragSelector: null,
    /**
     * A CSS selector used to identify child element(s) that should not trigger drag.
     * @config {String}
     */
    ignoreSelector: null,
    /**
     * The number of milliseconds after a pointerup to ignore click events on the document. This
     * is used to avoid the "up" event itself generating a `click` on the target.
     * @config {Number}
     * @default
     */
    dragSwallowClickTime: 50,
    /**
     * The amount of pixels to move pointer/mouse before it counts as a drag operation.
     * @config {Number}
     * @default
     */
    dragThreshold: 5,
    /**
     * The number of milliseconds that must elapse after a `touchstart` event before it is considered a drag. If
     * movement occurs before this time, the drag is aborted. This is to allow touch swipes and scroll gestures.
     * @config {Number}
     * @default
     */
    dragTouchStartDelay: 300,
    /**
     * The CSS selector to use to identify the closest valid target from the event target.
     * @config {String}
     */
    dropTargetSelector: null,
    /**
     * The {@link #config-dragSelector} item the mouse is currently over.
     * @member {HTMLElement} overItem
     * @readonly
     */
    overItem: null,
    testConfig: {
      dragSwallowClickTime: 50
    }
  }), __publicField(_a5, "properties", {
    /**
     * The CSS class to add to the {@link #config-dragRootElement} (or {@link #config-draggingClsSelector} from
     * there) as soon as the pointerdown event occurs.
     * @member {String}
     * @readonly
     */
    draggingCls: "b-draggable-active",
    /**
     * The CSS class to add to the `body` element as soon as the {@link #config-dragThreshold} is reached and
     * an actual drag is in progress.
     * @member {String}
     * @readonly
     */
    draggingBodyCls: "b-draghelper-active",
    // match DragHelper since we need the same treatment
    /**
     * The CSS class to add to the element being dragged as soon as the pointerdown event occurs.
     * @member {String}
     * @readonly
     */
    draggingItemCls: "b-dragging-item",
    /**
     * The CSS class to add to the {@link #config-dragRootElement} (or {@link #config-draggingClsSelector} from
     * there) as soon as the {@link #config-dragThreshold} is reached and an actual drag is in progress.
     * @member {String}
     * @readonly
     */
    draggingStartedCls: "b-draggable-started",
    /**
     * The CSS class that is added to the {@link #config-dragRootElement}, i.e., `'b-draggable'`.
     * @property {String}
     * @readonly
     */
    draggableCls: "b-draggable"
  }), _a5;
};

// ../Core/lib/Core/mixin/Droppable.js
var Droppable_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    //region Configs
    static get configurable() {
      return {
        /**
         * A selector, which, if specified, narrows the dropability to child elements of the
         * {@link #config-dropRootElement} which match this selector.
         * @config {String}
         */
        droppableSelector: null,
        /**
         * The current `DragContext`. This is set when a drag enters this target. Changing this config causes the
         * {@link #function-dragEnter} and {@link #function-dragLeave} methods to be called. If `dragEnter` returns
         * `false` for a drag, this value will be set to `null`.
         * @member {Core.util.drag.DragContext}
         * @readonly
         */
        dropping: null,
        /**
         * Set this config to the element where drops should be received. When set, the `b-droppable` CSS class is
         * added to the element and the `Droppable` instance is associated with that element so that it can be
         * found by {@link Core.mixin.Draggable draggables}.
         * @config {HTMLElement}
         */
        dropRootElement: {
          $config: "nullify",
          value: null
        }
      };
    }
    /**
     * Return the `Events` instance from which drop events are fired.
     * @internal
     * @property {Core.mixin.Events}
     */
    get dropEventer() {
      return this.trigger ? this : null;
    }
    /**
     * Returns the CSS class that is added to the {@link #config-dropRootElement}, i.e., `'b-droppable'`.
     * @property {String}
     * @readonly
     */
    get droppableCls() {
      return "b-droppable";
    }
    //endregion
    //region Drop Management
    /**
     * This method is called when a drag enters this droppable's `dropRootElement`. In many cases, this method is used
     * to create some sort of drop indicator to provide user feedback.
     *
     * If this method does not return `false`, the {@link #property-dropping} config will retain the given `drag` context
     * which was set prior to this method being called.
     *
     * If this method returns `false`, the drop will not be accepted. Neither {@link #function-dragDrop} nor
     * {@link #function-dragLeave} will be called for this drop. If the drag leaves this target and re-enters, this
     * method will be called again. While `dropping` will already be updated before this method is called, it will be
     * reset to `null` in this case.
     *
     * The base class implementation of this method fires the {@link #event-dragEnter} event.
     * @param {Core.util.drag.DragContext} drag
     * @returns {Boolean}
     */
    dragEnter(drag) {
      var _a6;
      return (_a6 = this.dropEventer) == null ? void 0 : _a6.trigger("dragEnter", { drag, event: drag.event });
    }
    /**
     * This method is called when the drag that was previously announced via {@link #function-dragEnter} moves to a new
     * position. This is typically where drop indicators are updated to reflect the new position.
     *
     * The base class implementation of this method fires the {@link #event-dragMove} event.
     * @param {Core.util.drag.DragContext} drag
     */
    dragMove(drag) {
      var _a6;
      return (_a6 = this.dropEventer) == null ? void 0 : _a6.trigger("dragMove", { drag, event: drag.event });
    }
    /**
     * This method is called when the drag that was previously announced via {@link #function-dragEnter} has ended with
     * a drop. In addition to any cleanup (since {@link #function-dragLeave} will not be called), this method handles
     * any updates associated with the data from the drag context and the position of the drop.
     *
     * The base class implementation of this method fires the {@link #event-drop} event.
     * @param {Core.util.drag.DragContext} drag
     */
    dragDrop(drag) {
      var _a6;
      return (_a6 = this.dropEventer) == null ? void 0 : _a6.trigger("drop", { drag, event: drag.event });
    }
    /**
     * This method is called when the drag that was previously announced via {@link #function-dragEnter} leaves this
     * droppable's `dropRootElement`, or the drag is {@link Core.util.drag.DragContext#property-aborted} by the user
     * pressing the `ESC` key, or the {@link Core.util.drag.DragContext#function-abort} method is called.
     *
     * This is the time to cleanup anything created by `dragEnter`.
     *
     * The base class implementation of this method fires the {@link #event-dragLeave} event.
     * @param {Core.util.drag.DragContext} drag
     */
    dragLeave(drag) {
      var _a6;
      return (_a6 = this.dropEventer) == null ? void 0 : _a6.trigger("dragLeave", { drag, event: drag.event });
    }
    //endregion
    //region Configs
    changeDropping(dropping, was) {
      if (dropping !== was) {
        const me = this;
        if (was) {
          if (was.aborted || !was.completed) {
            me.dragLeave(was);
          }
        }
        if (dropping) {
          me._dropping = dropping;
          if (me.dragEnter(dropping) === false) {
            dropping = null;
          }
          me._dropping = was;
        }
      }
      return dropping;
    }
    updateDropRootElement(rootEl, was) {
      const me = this, { droppableCls } = me;
      let droppables, i, removeCls;
      if (was) {
        droppables = DomDataStore.get(was, "droppables");
        removeCls = true;
        if (Array.isArray(droppables) && (i = droppables.indexOf(me)) > -1) {
          if (droppables.length < 2) {
            DomDataStore.remove(was, "droppables");
          } else {
            droppables.splice(i, 1);
            droppables.forEach((d) => {
              if (droppableCls === d.droppableCls) {
                removeCls = false;
              }
            });
          }
        }
        removeCls && was.classList.remove(droppableCls);
      }
      if (rootEl) {
        droppables = DomDataStore.get(rootEl, "droppables");
        if (droppables) {
          droppables.push(me);
        } else {
          DomDataStore.set(rootEl, "droppables", [me]);
        }
        rootEl.classList.add(droppableCls);
      }
    }
    //endregion
  }, __publicField(_a5, "$name", "Droppable"), _a5;
};

// ../Core/lib/Core/mixin/Featureable.js
var Featureable_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    static get configurable() {
      return {
        /**
         * Specifies the features to create and associate with the instance. The keys of this object are the names
         * of features. The values are config objects for those feature instances.
         *
         * After construction, this property can be used to access the feature instances and even reconfigure them.
         *
         * For example:
         * ```
         *  instance.features.amazing = {
         *      // reconfigure this feature
         *  }
         * ```
         * This can also be done in bulk:
         * ```
         *  instance.features = {
         *      amazing : {
         *          // reconfigure this feature
         *      },
         *      // reconfigure other features
         *  }
         * ```
         * @config {Object}
         */
        features: null
      };
    }
    static get declarable() {
      return [
        /**
         * This property getter returns options that control feature management for the derived class. This
         * property getter must be defined by the class that mixes in `Featureable` in order to initialize the
         * class properly.
         * ```
         *  class SuperWidget extends Widget.mixin(Featureable) {
         *      static get featureable() {
         *          return {
         *              factory : SuperWidgetFeature
         *          };
         *      }
         *      ...
         *  }
         * ```
         * @static
         * @member {Object} featureable
         * @property {Core.mixin.Factoryable} featureable.factory The factoryable class (not one of its instances)
         * that will be used to create feature instances.
         * @property {String} [featureable.ownerName='client'] The config or property to assign on each feature as
         * a reference to its creator, the `Featureable` instance.
         * @internal
         */
        "featureable"
      ];
    }
    static setupFeatureable(cls) {
      const featureable = {
        ownerName: "client",
        ...cls.featureable
      };
      featureable.factory.initClass();
      Reflect.defineProperty(cls, "featureable", {
        get() {
          return featureable;
        }
      });
    }
    doDestroy() {
      var _a6;
      const features = this.features;
      super.doDestroy();
      for (const name in features) {
        const feature = features[name];
        (_a6 = feature.destroy) == null ? void 0 : _a6.call(feature);
      }
    }
    /**
     * Returns `true` if the specified feature is active for this instance and `false` otherwise.
     * @param {String} name The feature name
     * @returns {Boolean}
     */
    hasFeature(name) {
      var _a6;
      return Boolean((_a6 = this.features) == null ? void 0 : _a6[name]);
    }
    changeFeatures(features, was) {
      if (this.isDestroying) {
        return;
      }
      const me = this, { featureable } = me.constructor, manager = me.$features || (me.$features = new DynamicObject({
        configName: "features",
        factory: featureable.factory,
        owner: me,
        ownerName: featureable.ownerName
      }));
      manager.update(features);
      if (!was) {
        return manager.target;
      }
    }
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
    getCurrentConfig(options) {
      const result = super.getCurrentConfig(options), { features } = result;
      if (features) {
        for (const featureName in features) {
          if (Object.keys(features[featureName]).length === 0) {
            features[featureName] = true;
          }
        }
      }
      return result;
    }
  }, __publicField(_a5, "$name", "Featureable"), _a5;
};

// ../Core/lib/Core/mixin/Hoverable.js
var EDGES = {
  e: "b-hover-edge",
  t: "b-hover-top",
  r: "b-hover-right",
  b: "b-hover-bottom",
  l: "b-hover-left"
};
var EDGE_CLASSES = {
  [EDGES.e]: 1,
  [EDGES.t]: 1,
  [EDGES.r]: 1,
  [EDGES.b]: 1,
  [EDGES.l]: 1
};
var ZONES = {
  t: [EDGES.e, EDGES.t],
  r: [EDGES.e, EDGES.r],
  b: [EDGES.e, EDGES.b],
  l: [EDGES.e, EDGES.l],
  tr: [EDGES.e, EDGES.t, EDGES.r],
  bl: [EDGES.e, EDGES.b, EDGES.l],
  tl: [EDGES.e, EDGES.t, EDGES.l],
  br: [EDGES.e, EDGES.b, EDGES.r]
};
var Hoverable_default = (Target) => {
  var _a5;
  return _a5 = class extends Target.mixin(Delayable_default) {
    //region Configs
    static get configurable() {
      return {
        /**
         * A CSS class to add to the {@link #config-hoverTarget target} element.
         * @config {String}
         */
        hoverCls: null,
        /**
         * A CSS class to add to the {@link #config-hoverTarget target} element to enable CSS animations. This class
         * is added after calling {@link #function-hoverEnter}.
         * @config {String}
         */
        hoverAnimationCls: null,
        /**
         * A CSS class to add to the {@link #config-hoverRootElement root} element.
         * @config {String}
         */
        hoverRootCls: null,
        /**
         * A CSS class to add to the {@link #config-hoverRootElement root} element when there is an active
         * {@link #config-hoverTarget target}.
         * @config {String}
         */
        hoverRootActiveCls: null,
        /**
         * The number of milliseconds to delay notification of changes in the {@link #config-hoverTarget}.
         * @config {Number}
         */
        hoverDelay: null,
        /**
         * The current element that the cursor is inside as determined by `mouseover` and `mouseout`. Changes in
         * this config trigger re-evaluation of the {@link #config-hoverSelector} to determine if there is a
         * {@link #config-hoverTarget}.
         * @config {HTMLElement}
         * @private
         */
        hoverElement: null,
        /**
         * An element to ignore. Mouse entry into this element will not trigger a change in either of the
         * {@link #config-hoverElement} or {@link #config-hoverTarget} values.
         * @config {HTMLElement}
         */
        hoverIgnoreElement: null,
        /**
         * This property is a string containing one character for each edge that is hoverable. For example, a
         * value of "tb" indicates that the top and bottom edges are hoverable.
         * @config {String}
         */
        hoverEdges: null,
        /**
         * When {@link #config-hoverEdges} is used, this value determines the size (in pixels) of the edge. When
         * the cursor is within this number of pixels of an edge listed in `hoverEdges`, the appropriate CSS class
         * is added to the {@link #config-hoverTarget}:
         *
         *  - `b-hover-top`
         *  - `b-hover-right`
         *  - `b-hover-bottom`
         *  - `b-hover-left`
         *
         * Depending on the values of `hoverEdges`, it is possible to have at most two of these classes present at
         * any one time (when the cursor is in a corner).
         * @config {Number}
         * @default
         */
        hoverEdgeSize: 10,
        /**
         * The outer element where hover tracking will operate (attach events to it and use as root limit when
         * looking for ancestors).
         *
         * A common choice for this will be `document.body`.
         * @config {HTMLElement}
         */
        hoverRootElement: {
          $config: "nullify",
          value: null
        },
        /**
         * A selector for the [closest](https://developer.mozilla.org/en-US/docs/Web/API/Element/closest) API to
         * determine the actual element of interest. This selector is used to process changes to the
         * {@link #config-hoverElement} to determine the {@link #config-hoverTarget}.
         * @config {String}
         */
        hoverSelector: null,
        /**
         * The currently active hover target. This will be the same as {@link #config-hoverElement} unless there is
         * a {@link #config-hoverSelector}.
         * @config {HTMLElement}
         */
        hoverTarget: {
          $config: "nullify",
          value: null
        },
        /**
         * Set to `true` to include tracking of `mousemove` events for the active {@link #config-hoverTarget}. This
         * is required for the {@link #function-hoverMove} method to be called.
         * @config {Boolean}
         * @default false
         */
        hoverTrack: null,
        /**
         * A string value containing one character per active edge (e.g., "tr").
         * @config {String}
         * @private
         */
        hoverZone: null
      };
    }
    static get delayable() {
      return {
        setHoverTarget: 0
      };
    }
    //endregion
    //region State Handling
    /**
     * This method is called when the cursor enters the {@link #config-hoverTarget}. The `hoverTarget` will not be
     * `null`.
     * @param {HTMLElement} leaving The element that was previously the `hoverTarget`. This value may be null.
     */
    hoverEnter(leaving) {
    }
    /**
     * This method should return true if the given `element` should be ignored. By default, this is `true` if the
     * `element` is contained inside the {@link #config-hoverIgnoreElement}.
     * @param {HTMLElement} element
     * @returns {Boolean}
     * @protected
     */
    hoverIgnore(element) {
      var _a6;
      return (_a6 = this.hoverIgnoreElement) == null ? void 0 : _a6.contains(element);
    }
    /**
     * This method is called when the cursor leaves the {@link #config-hoverTarget}. The `hoverTarget` may be `null`
     * or refer to the new `hoverTarget`
     * @param {HTMLElement} leaving The element that was previously the `hoverTarget`. This value will not be null.
     */
    hoverLeave(leaving) {
    }
    /**
     * This method is called when the mouse moves within a {@link #config-hoverTarget}, but only if enabled by the
     * {@link #config-hoverTrack} config.
     * @param {Event} event
     */
    hoverMove(event) {
    }
    //endregion
    //region Events
    onHoverMouseMove(event) {
      const me = this, { hoverEdges, hoverEdgeSize, hoverTarget } = me;
      if (hoverTarget) {
        if (hoverEdges) {
          const { top, left, width, height, right, bottom } = hoverTarget.getBoundingClientRect(), { clientX, clientY } = event, centerX = left + width / 2, centerY = top + height / 2, t = clientY < (hoverEdgeSize ? top + hoverEdgeSize : centerY), r = clientX >= (hoverEdgeSize ? right - hoverEdgeSize : centerX), b = clientY >= (hoverEdgeSize ? bottom - hoverEdgeSize : centerY), l = clientX < (hoverEdgeSize ? left + hoverEdgeSize : centerX), tb = t || b ? t ? "t" : "b" : "", rl = r || l ? r ? "r" : "l" : "";
          me.hoverZone = (hoverEdges.includes(tb) ? tb : "") + (hoverEdges.includes(rl) ? rl : "");
        }
        me.hoverEvent = event;
        me.hoverTrack && me.hoverMove(event);
      }
    }
    onHoverMouseOver(event) {
      this.hoverEvent = event;
      this.hoverElement = event.target;
    }
    onHoverMouseOut(event) {
      this.hoverEvent = event;
      this.hoverElement = event.relatedTarget;
    }
    //endregion
    //region Configs
    // hoverDelay
    updateHoverDelay(delay2) {
      this.setHoverTarget.delay = delay2;
    }
    // hoverEdges
    changeHoverEdges(edges) {
      return edges === true ? "trbl" : (edges || "").replace("v", "tb").replace("h", "lr");
    }
    updateHoverEdges() {
      this.syncHoverListeners();
    }
    // hoverElement
    changeHoverElement(element) {
      if (!this.hoverIgnore(element)) {
        return element;
      }
    }
    updateHoverElement(hoverEl) {
      const { hoverSelector } = this;
      if (hoverSelector) {
        hoverEl = hoverEl == null ? void 0 : hoverEl.closest(hoverSelector);
      }
      this.setHoverTarget(hoverEl);
    }
    // hoverRootElement
    updateHoverRootElement(rootEl, was) {
      const { hoverRootCls } = this;
      if (hoverRootCls) {
        was == null ? void 0 : was.classList.remove(hoverRootCls);
        rootEl == null ? void 0 : rootEl.classList.add(hoverRootCls);
      }
      this.syncHoverListeners();
    }
    // hoverTarget
    changeHoverTarget(hoverEl, was) {
      if (was) {
        this.hoverZone = null;
      }
      return hoverEl;
    }
    updateHoverTarget(hoverEl, was) {
      const me = this, { hoverCls, hoverAnimationCls, hoverRootActiveCls, hoverRootElement } = me;
      if (hoverRootActiveCls) {
        hoverRootElement == null ? void 0 : hoverRootElement.classList[hoverEl ? "add" : "remove"](hoverRootActiveCls);
      }
      if (was) {
        hoverCls && was.classList.remove(hoverCls);
        hoverAnimationCls && was.classList.remove(hoverAnimationCls);
        me.hoverLeave(was);
      }
      if (hoverEl) {
        hoverCls && hoverEl.classList.add(hoverCls);
        me.hoverEnter(was);
        if (me.hoverTrack) {
          me.hoverMove(me.hoverEvent);
        }
        if (hoverAnimationCls) {
          hoverEl.getBoundingClientRect();
          hoverEl.classList.add(hoverAnimationCls);
        }
      }
    }
    // hoverTrack
    updateHoverTrack() {
      this.syncHoverListeners();
    }
    // hoverZone
    updateHoverZone(zone) {
      const { hoverAnimationCls, hoverTarget } = this;
      if (hoverTarget) {
        const { className } = hoverTarget, cls = DomClassList.change(
          className,
          /* add= */
          zone ? ZONES[zone] : null,
          /* remove= */
          EDGE_CLASSES
        );
        if (className !== cls) {
          hoverTarget.className = cls;
          if (zone && hoverAnimationCls) {
            hoverTarget.classList.remove(hoverAnimationCls);
            hoverTarget.getBoundingClientRect();
            hoverTarget.classList.add(hoverAnimationCls);
          }
        }
      }
    }
    //endregion
    //region Misc
    setHoverTarget(target) {
      this.hoverTarget = target;
    }
    syncHoverListeners() {
      var _a6;
      const me = this, element = me.hoverRootElement, listeners = {
        element,
        thisObj: me,
        mouseover: "onHoverMouseOver",
        mouseout: "onHoverMouseOut"
      };
      if (me.hoverTrack || me.hoverEdges) {
        listeners.mousemove = "onHoverMouseMove";
      }
      (_a6 = me._hoverRootDetacher) == null ? void 0 : _a6.call(me);
      me._hoverRootDetacher = element && EventHelper.on(listeners);
    }
    //endregion
  }, __publicField(_a5, "$name", "Hoverable"), _a5;
};

// ../Core/lib/Core/mixin/LoadMaskable.js
var LoadMaskable_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    static get configurable() {
      return {
        /**
         * A {@link Core.widget.Mask} config object to adjust the {@link Core.widget.Widget#config-maskDefaults}
         * when data is loading. The message and optional configuration from the
         * {@link Core.mixin.LoadMaskable#config-loadMask} config take priority over these options, just as they do
         * for `maskDefaults`, respectively.
         *
         * The final mask configuration for a load mask is as if the following were applied:
         *
         * ```
         *  Object.assign({},
         *      widget.maskDefaults,
         *      widget.loadMaskDefaults,
         *      widget.loadMask);
         * ```
         * @config {MaskConfig}
         * @category Masking
         */
        loadMaskDefaults: {
          useTransition: true,
          showDelay: 1e3
        },
        /**
         * A {@link Core.widget.Mask} config object to adjust the {@link Core.widget.Widget#config-maskDefaults}
         * when an error occurs loading data.
         *
         * Set to `false` to disable showing data loading error mask.
         *
         * The final mask configuration for an error mask is as if the following were applied:
         *
         * ```
         *  Object.assign({},
         *      widget.maskDefaults,
         *      widget.loadMaskDefaults,
         *      widget.loadMaskError,
         *      errorMessage);
         * ```
         * @config {MaskConfig|Core.widget.Mask|Boolean}
         * @category Masking
         */
        loadMaskError: {
          icon: "b-icon b-icon-warning",
          autoClose: 3e3,
          showDelay: 0
        },
        /**
         * A {@link Core.widget.Mask} config object, or a message to be shown when a store is performing a remote
         * operation, or Crud Manager is loading data from the sever. Set to `null` to disable default load mask.
         *
         * @config {String|MaskConfig|null}
         * @default "Loading..."
         * @category Masking
         */
        loadMask: {
          text: "L{GridBase.loadMask}"
        },
        /**
         * A {@link Core.widget.Mask} config object, or a message to be shown when Crud Manager
         * is persisting changes on the server. Set to `null` to disable default sync mask.
         *
         * This config is similar to {@link Core.mixin.LoadMaskable#config-loadMask} but designed for saving data.
         *
         * To create a custom sync mask need to subscribe to the Crud Manager events and show
         * {@link Core.widget.Mask Mask} on `beforeSend` and hide it on `requestDone` and `requestFail`.
         *
         * To create a custom sync mask, set this config to `null` and subscribe to the CrudManager's events to
         * show or hide the {@link Core.widget.Widget#config-masked mask} as desired.
         *
         * ```javascript
         *  widget.crudManager.on({
         *      loadStart() {
         *          widget.masked = {
         *              text : 'Data is loading...'
         *          };
         *      },
         *      load() {
         *          widget.masked = null;
         *      },
         *      loadCanceled() {
         *          widget.masked = null;
         *      },
         *      syncStart() {
         *          widget.masked = null;
         *      },
         *      sync() {
         *          widget.masked = null;
         *      },
         *      syncCanceled() {
         *          widget.masked = null;
         *      },
         *      requestFail({ response }) {
         *          widget.masked.error = response.message || 'Sync failed';
         *      }
         *  });
         *
         *  store.load();
         * ```
         *
         * @config {String|MaskConfig|null}
         * @default "Saving changes, please wait..."
         * @category Masking
         */
        syncMask: {
          text: "L{GridBase.syncMask}"
        },
        localizableProperties: ["loadMask.text", "syncMask.text"],
        testConfig: {
          loadMaskError: {
            icon: "b-icon b-icon-warning",
            autoClose: 500,
            showDelay: 0
          }
        }
      };
    }
    /**
     * Applies the {@link Core.mixin.LoadMaskable#config-loadMask} as the {@link Core.widget.Widget#config-masked mask}
     * for this widget.
     * @returns {Core.widget.Mask}
     * @internal
     */
    applyLoadMask() {
      const me = this, { loadMask } = me;
      if (loadMask) {
        me.masked = Mask.mergeConfigs(me.loadMaskDefaults, loadMask);
      }
      return me.masked;
    }
    /**
     * Updates the current {@link Core.widget.Widget#config-masked mask} for this widget to present the specified
     * `error`.
     * @param {String} error The error message to display in the mask.
     * @returns {Core.widget.Mask}
     * @internal
     */
    applyMaskError(error) {
      const { loadMaskError, masked } = this;
      if (loadMaskError === false) {
        masked.hide();
      } else if (masked) {
        ObjectHelper.assign(masked.errorDefaults, loadMaskError);
        masked.error = error;
      }
      return masked;
    }
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "LoadMaskable"), _a5;
};

// ../Core/lib/Core/mixin/Override.js
var excludedPropNames = {
  constructor: 1,
  prototype: 1,
  name: 1,
  length: 1,
  arguments: 1,
  caller: 1,
  callee: 1,
  __proto__: 1
};
var Override = class {
  /**
   * Apply override. We strongly suggest that you at least specify a maxVersion for your overrides.
   * ```
   * class OriginalOverride {
   *     static get target() {
   *         return {
   *             class      : Original,
   *             product    : 'grid',
   *             minVersion : '1.0',
   *             maxVersion : '1.5'
   *         }
   *     }
   * }
   * ```
   * @param {Object} override An override class definition
   */
  static apply(override) {
    if (!override.target)
      throw new Error("Override must specify what it overrides, using static getter target");
    if (!override.target.class)
      throw new Error("Override must specify which class it overrides, using target.class");
    if (!this.shouldApplyOverride(override))
      return false;
    const staticKeys = Object.getOwnPropertyNames(override), instanceKeys = Object.getOwnPropertyNames(override.prototype);
    staticKeys.splice(staticKeys.indexOf("target"), 1);
    this.internalOverrideAll(override.target.class, staticKeys, override);
    this.internalOverrideAll(override.target.class.prototype, instanceKeys, override.prototype);
    return true;
  }
  static internalOverrideAll(targetClass, properties, overrideDefinition) {
    Reflect.ownKeys(overrideDefinition).forEach((key) => {
      if (properties.includes(key) && !excludedPropNames[key]) {
        const desc = Object.getOwnPropertyDescriptor(overrideDefinition, key);
        let currentTargetClass = targetClass;
        let targetProperty = null;
        while (!targetProperty && currentTargetClass) {
          targetProperty = Object.getOwnPropertyDescriptor(currentTargetClass, key);
          if (!targetProperty) {
            currentTargetClass = Object.getPrototypeOf(currentTargetClass);
          }
        }
        if (targetProperty) {
          this.internalOverride(currentTargetClass, key, desc, targetProperty);
        }
      }
    });
  }
  static internalOverride(target, key, desc, targetDesc) {
    const overrides = target._overridden = target._overridden || {};
    overrides[key] = target[key];
    if (targetDesc.get) {
      Object.defineProperty(target, key, {
        enumerable: false,
        configurable: true,
        get: desc.get
      });
    } else {
      target[key] = desc.value;
    }
  }
  /**
   * Checks versions if an override should be applied. Specify version in your overrides target config
   *
   * ```javascript
   * class OriginalOverride {
   *     static get target() {
   *         return {
   *             class      : Original,
   *             product    : 'grid',
   *             minVersion : '1.0',
   *             maxVersion : '1.5'
   *         }
   *     }
   * }
   * ```
   *
   * @param override
   * @returns {Boolean}
   * @private
   */
  static shouldApplyOverride(override) {
    const config = override.target;
    if (!config.maxVersion && !config.minVersion)
      return true;
    if (!config.product)
      throw new Error("Override must specify product when using versioning");
    if (config.maxVersion && VersionHelper[config.product].isNewerThan(config.maxVersion)) {
      return false;
    }
    if (config.minVersion && VersionHelper[config.product].isOlderThan(config.minVersion)) {
      return false;
    }
    return true;
  }
};
Override._$name = "Override";

// ../Core/lib/Core/util/DayTime.js
var MILLIS_PER_MINUTE = 60 * 1e3;
var MILLIS_PER_HOUR = 60 * MILLIS_PER_MINUTE;
var MILLIS_PER_DAY = 24 * MILLIS_PER_HOUR;
var timeRe = /(\d+)?:?(\d*)/;
var DayTime = class _DayTime {
  /**
   * Returns a string of "HH:MM" for a given time of day in milliseconds.
   * @param {Number} timeOfDay The time of day in milliseconds.
   * @returns {String}
   * @private
   */
  static format(timeOfDay) {
    const h = Math.floor(timeOfDay / MILLIS_PER_HOUR), m = Math.floor(timeOfDay / MILLIS_PER_MINUTE) % 60;
    return `${h}:${m < 10 ? "0" : ""}${m}`;
  }
  /**
   * Parses a time of day which may be a number (0-24 for the hour of the day) or a string in "H:MM" format and
   * returns the time of day as a number of milliseconds.
   *
   * If `time` is a `Date` instance, its time of day is returned.
   * @param {Date|Number|String} time
   * @returns {Number}
   * @private
   */
  static parse(time) {
    const type = typeof time;
    if (type === "string") {
      const match = timeRe.exec(time);
      time = Number(match[1] || 0) * MILLIS_PER_HOUR + Number(match[2] || 0) * MILLIS_PER_MINUTE;
    } else if (type !== "number") {
      time = DateHelper.getTimeOfDay(time);
    } else if (time <= 24) {
      time *= MILLIS_PER_HOUR;
    }
    return Math.min(Math.max(Math.floor(time), 0), MILLIS_PER_DAY);
  }
  constructor(config) {
    let startShift = 0, startTime, endTime;
    if (config == null ? void 0 : config.isDayView) {
      startShift = config.dayStartShift;
      startTime = config.dayStartTime;
      endTime = config.dayEndTime;
    } else if (typeof config === "number") {
      startShift = startTime = endTime = config;
    } else if (config) {
      startShift = config.startShift;
      startTime = config.timeStart;
      endTime = config.timeEnd;
    }
    this.startShift = startShift = _DayTime.parse(startShift || 0);
    this.timeEnd = (endTime == null ? (startShift + MILLIS_PER_DAY) % MILLIS_PER_DAY : _DayTime.parse(endTime)) || MILLIS_PER_DAY;
    this.timeStart = startTime == null ? startShift : _DayTime.parse(startTime);
  }
  get startHour() {
    return Math.floor(this.timeStart / MILLIS_PER_HOUR);
  }
  get endHour() {
    return Math.floor(this.timeEnd / MILLIS_PER_HOUR);
  }
  /**
   * The number of milliseconds from the day's `startShift` to its `timeStart`.
   * @member {Number}
   */
  get startTimeOffsetMs() {
    const { startShift, timeStart } = this;
    return timeStart < startShift ? MILLIS_PER_DAY - startShift + timeStart : timeStart - startShift;
  }
  /**
   * The `Date` object for the most recently started, shifted day. The time of this `Date` will be the `startShift`.
   * It is possible for this date to be yesterday on a midnight-based calendar. For example, if the `startShift` is
   * 6PM and the current time is 6AM on May 20, this value will be 6PM of May 19 (the most recently started day).
   * @member {Date}
   */
  get today() {
    return this.startOfDay(/* @__PURE__ */ new Date());
  }
  /**
   * Returns `Date` object for the nearest (shifted) day ending after the given `date`. The time of this `Date` will
   * be the `startShift`.
   *
   * It is possible for this date to be in the next day on a midnight-based calendar. For example, if the `startShift`
   * is 6PM and `date` is 7PM on May 20, this method will return 6PM of May 21 (the nearest day ending).
   * @param {Date} date The date for which to find the nearest day ending.
   * @returns {Date}
   */
  ceil(date2) {
    const ret = this.startOfDay(date2);
    if (ret < date2) {
      ret.setDate(ret.getDate() + 1);
    }
    return ret;
  }
  /**
   * Returns `true` if the time of day for the given `date` is between `timeStart` and `timeEnd`.
   * @param {Date|Number|String} date The hour number, 'HH:MM' time or a `Date` instance to test.
   * @returns {Boolean}
   */
  contains(date2) {
    return !this.outside(date2);
  }
  /**
   * Returns a "YYYY-MM-DD" string for the given `date`. This value will match the `date` if the time of day is at or
   * after `startShift`, but will be the prior date otherwise.
   * @param {Date|Number} date The date from which to compute the 'YYYY-MM-DD' key.
   * @returns {String}
   */
  dateKey(date2) {
    date2 = this.shiftDate(date2, -1);
    return DateHelper.makeKey(date2);
  }
  /**
   * Returns a `Date` instance with `startShift` as the time of day and the Y/M/D of the given `date`.
   * @param {Date} date The date's year, month, and day values.
   * @returns {Date}
   */
  dayOfDate(date2) {
    return this.shiftDate(DateHelper.clearTime(date2));
  }
  /**
   * Returns the day of week (0-8) for the given `date`. This value will match the `date` if the time of day is at or
   * after `startShift`, but will be the prior day otherwise.
   * @param {Date|Number} date The date from which to compute the day of week.
   * @returns {Number}
   */
  dayOfWeek(date2) {
    date2 = this.shiftDate(date2, -1);
    return date2.getDay();
  }
  /**
   * Returns the difference between the time of day of the given `date` and `timeStart` in the specified time `unit`.
   * @param {Date|Number|String} date The hour number, 'HH:MM' time or a `Date` instance.
   * @param {String} unit The desired unit of time to return (see {@link Core.helper.DateHelper#function-as-static}).
   * @returns {Number}
   */
  delta(date2, unit = "ms") {
    const { timeStart } = this, time = _DayTime.parse(date2), t = (this.startShift && time < timeStart ? time + MILLIS_PER_DAY : time) - timeStart;
    return unit === "ms" ? t : DateHelper.as(unit, t, "ms");
  }
  /**
   * Returns the duration of the visible day (between `timeStart` and `timeEnd`) in the specified time `unit`.
   * @param {String} unit The desired unit of time to return (see {@link Core.helper.DateHelper#function-as-static}).
   * @returns {Number}
   */
  duration(unit = "ms") {
    const { timeStart, timeEnd } = this, millis = timeStart < timeEnd ? timeEnd - timeStart : MILLIS_PER_DAY - timeStart + timeEnd;
    return unit === "ms" ? millis : DateHelper.as(unit, millis, "ms");
  }
  /**
   * Returns `true` if this instance describes the same day as the `other`.
   * @param {Core.util.DayTime} other The other instance to which `this` instance should be tested for equality.
   * @returns {Boolean}
   */
  equals(other) {
    return this.startShift === (other == null ? void 0 : other.startShift) && this.timeStart === other.timeStart && this.timeEnd === other.timeEnd;
  }
  /**
   * Returns `true` if the times of day described by `startDate` and `endDate` intersect the visible time of this day.
   * @param {Date} startDate The start date of the date range or an event record containing both startDate and endDate
   * fields.
   * @param {Date} [endDate] The end date if `startDate` is not an event record.
   * @returns {Boolean}
   */
  intersects(startDate, endDate) {
    const me = this, { timeStart, timeEnd } = me, [date0, date1] = me._dateRangeArgs(startDate, endDate), [start, end] = me.timeRange(date0, date1);
    if (timeStart < timeEnd) {
      if (start < end) {
        return start < timeEnd && timeStart < end;
      }
      return start < timeEnd || timeStart < end;
    }
    return !(start < end) || start < timeEnd || timeStart < end;
  }
  /**
   * Returns `true` if the given date range is contained within one day.
   * @param {Date} startDate The start date of the date range or an event record containing both startDate and endDate
   * fields.
   * @param {Date} [endDate] The end date if `startDate` is not an event record.
   * @returns {Boolean}
   */
  isIntraDay(startDate, endDate) {
    const me = this, [date0, date1] = me._dateRangeArgs(startDate, endDate), dayStart = me.startOfDay(date0), diff = MILLIS_PER_DAY - DateHelper.diff(dayStart, date1, "ms");
    if (diff < 0) {
      return false;
    }
    return diff > 0 || dayStart < date0;
  }
  /**
   * Returns `true` if the given date range or event crosses the day boundary.
   * @param {Date} startDate The start date of the date range or an event record containing both startDate and endDate
   * fields.
   * @param {Date} [endDate] The end date if `startDate` is not an event record.
   * @returns {Boolean}
   */
  isInterDay(timeSpan) {
    return timeSpan.allDay || !this.isIntraDay(...arguments);
  }
  /**
   * Returns -1, 0, or 1 based on whether the time of day for the given `date` is before `timeStart` (-1), or after
   * `timeEnd` (1), or between these times (0).
   * @param {Date|Number|String} date The hour number, 'HH:MM' time or a `Date` instance to test.
   * @returns {Number}
   */
  outside(date2) {
    const { startShift, timeStart, timeEnd } = this, time = _DayTime.parse(date2);
    if (timeStart < timeEnd) {
      if (time < timeStart) {
        return time < startShift ? 1 : -1;
      }
      if (time < timeEnd) {
        return 0;
      }
      return time < startShift ? -1 : 1;
    }
    if (time < timeEnd || time >= timeStart) {
      return 0;
    }
    return time < startShift ? 1 : -1;
  }
  parseKey(key) {
    return this.dayOfDate(DateHelper.parseKey(key));
  }
  /**
   * Returns the given `date` shifted forward (`direction` > 0) or backward (`direction` < 0) by the `startShift`.
   * @param {Number|Date} date The date as a `Date` or the millisecond UTC epoch.
   * @param {Number} direction A value > 0 to shift `date` forward, or < 0 to shift it backwards.
   * @returns {Date}
   */
  shiftDate(date2, direction = 1) {
    const { startShift } = this, type = typeof date2;
    date2 = type === "number" ? new Date(date2) : type === "string" ? DateHelper.parse(date2) : new Date(date2.getTime());
    if (direction && startShift) {
      date2.setMilliseconds(direction > 0 ? startShift : -startShift);
    }
    return date2;
  }
  /**
   * Sorts the given set of `events` by the maximum of `startDate` and `startOfDay` for the given `date`, followed
   * by `duration` in case of a tie.
   * @param {Date} date The day for which events are to be sorted.
   * @param {Object[]} events The events to sort, typically an `Scheduler.model.EventModel[]` but any objects with
   * both `startDate` and `endDate` fields are acceptable.
   * @returns {Object[]} The passed `events` array.
   * @internal
   */
  sortEvents(date2, events) {
    const startOfDay = this.startOfDay(date2);
    return events == null ? void 0 : events.sort((event1, event2) => {
      event1 = event1.eventRecord || event1;
      event2 = event2.eventRecord || event2;
      let { startDate: start1 } = event1, { startDate: start2 } = event2;
      if (!start1) {
        return -1;
      }
      if (!start2) {
        return 1;
      }
      start1 = start1 < startOfDay ? startOfDay : start1;
      start2 = start2 < startOfDay ? startOfDay : start2;
      return start1 - start2 || event2.endDate - start2 - (event1.endDate - start1);
    });
  }
  /**
   * Returns `Date` object for the nearest started (shifted) day prior to the given `date`. The time of this `Date`
   * will be the `startShift`.
   *
   * It is possible for this date to be in the prior day on a midnight-based calendar. For example, if the `startShift`
   * is 6PM and `date` is 6AM on May 20, this method will return 6PM of May 19 (the nearest started day).
   * @param {Date} date The date for which to find the nearest started day.
   * @returns {Date}
   */
  startOfDay(date2) {
    date2 = this.shiftDate(date2, -1);
    date2 = DateHelper.clearTime(date2);
    date2 = this.shiftDate(date2);
    return date2;
  }
  /**
   * Returns a range of {@link Core.helper.DateHelper#function-getTimeOfDay-static times of day} for the given
   * date range.
   * @param {Date} startDate The start date of the date range or an event record containing both `startDate` and `endDate` fields
   * @param {Date} [endDate] The end date if `startDate` is not an event record
   * @returns {Number[]}
   */
  timeRange(startDate, endDate) {
    const [start, end] = this._dateRangeArgs(startDate, endDate);
    return [DateHelper.getTimeOfDay(start), DateHelper.getTimeOfDay(end)];
  }
  toString() {
    const { startShift, timeEnd, timeStart } = this, suffix = startShift ? `@${_DayTime.format(startShift)}` : "", prefix = _DayTime.format(timeStart);
    if (timeStart === timeEnd) {
      return startShift ? suffix : prefix;
    }
    return `${prefix}-${_DayTime.format(timeEnd)}${suffix}`;
  }
  /**
   * Decodes the arguments and returns a pair of `Date` objects for the start and end of the date range.
   * @param {Date} startDate The start date of the date range or an event record containing both startDate and endDate
   * fields.
   * @param {Date} [endDate] The end date if `startDate` is not an event record.
   * @returns {Date[]}
   * @private
   */
  _dateRangeArgs(startDate, endDate) {
    return startDate.isModel ? [startDate.startDate, startDate.endingDate] : [startDate, endDate];
  }
};
DayTime.MIDNIGHT = new DayTime({
  startShift: 0,
  timeStart: 0,
  timeEnd: 24
});
DayTime.MILLIS_PER_MINUTE = MILLIS_PER_MINUTE;
DayTime.MILLIS_PER_HOUR = MILLIS_PER_HOUR;
DayTime.MILLIS_PER_DAY = MILLIS_PER_DAY;
DayTime._$name = "DayTime";

// ../Core/lib/Core/util/ElementVisitor.js
var _ElementVisitor = class _ElementVisitor extends Base {
  /**
   * Returns an ElementVisitor instance for the given element and selector.
   *
   * This is the recommended way to get an ElementVisitor instance as it will reuse instances.
   * @param {HTMLElement} element The element in which to visit selected descendants.
   * @param {String} selector The selector to use when visiting descendant nodes within the element.
   * @returns {Core.util.ElementVisitor} An ElementVisitor instance which visits the element's descendants based on the selector.
   */
  static getVisitor(element, selector) {
    const { elementVisitorMap } = this, selectorMap = elementVisitorMap.get(element) || elementVisitorMap.set(element, /* @__PURE__ */ new Map()).get(element), key = typeof selector === "string" ? selector : selector.toString();
    return selectorMap.get(key) || selectorMap.set(key, new _ElementVisitor({ element, selector })).get(key);
  }
  updateSelector(selector) {
    this.treeWalker = document.createTreeWalker(this.element, NodeFilter.SHOW_ELEMENT, typeof selector === "string" ? (node) => {
      var _a5;
      return ((_a5 = node.matches) == null ? void 0 : _a5.call(node, selector)) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
    } : selector);
  }
  /**
   * Returns the first element in the tree that matches the selector.
   * @returns {HTMLElement}
   */
  first() {
    this.treeWalker.currentNode = this.element;
    return this.treeWalker.firstChild();
  }
  /**
   * Returns the previous element in the tree that matches the selector.
   * @returns {HTMLElement}
   */
  previous(current) {
    if (current) {
      this.treeWalker.currentNode = current;
    }
    return this.treeWalker.previousNode();
  }
  /**
   * Returns the next element in the tree that matches the selector.
   * @returns {HTMLElement}
   */
  next(current) {
    if (current) {
      this.treeWalker.currentNode = current;
    }
    return this.treeWalker.nextNode();
  }
  /**
   * Returns the last element in the tree that matches the selector.
   * @returns {HTMLElement}
   */
  last() {
    this.treeWalker.currentNode = this.element;
    return this.treeWalker.lastChild();
  }
};
__publicField(_ElementVisitor, "$name", "ElementVisitor");
__publicField(_ElementVisitor, "configurable", {
  /**
   * The element to visit.
   * @config {HTMLElement} element
   */
  element: null,
  /**
   * The selector to use when visiting the element or a function which may accept of reject candidate nodes by returning
   * - NodeFilter.FILTER_ACCEPT
   * - NodeFilter.FILTER_REJECT
   * - NodeFilter.FILTER_SKIP
   *
   * @config {String|Function} selector
   * @param {Node} node The node to accept or reject.
   * @returns {Number} NodeFilter.FILTER_ACCEPT, NodeFilter.FILTER_REJECT, or NodeFilter.FILTER_SKIP.
   */
  selector: null
});
// Cache of selectors to ElementVisitor Maps based on the element.
// Entries will be evicted when the element is removed from the DOM and no longer referenced.
__publicField(_ElementVisitor, "elementVisitorMap", /* @__PURE__ */ new WeakMap());
var ElementVisitor = _ElementVisitor;
ElementVisitor._$name = "ElementVisitor";

// ../Core/lib/Core/widget/Hint.js
var Hint = class extends Popup {
  afterShow() {
    super.afterShow(...arguments);
    this.addDocumentMouseDownListener();
  }
  realign() {
    super.realign(...arguments);
    this.syncTargetHighlighting();
  }
  // The topmost modal is always focused by GlobalEvents, but hints must allow focus to be taken by
  // the application so that the actions can be executed on the target elements, so we are never the top modal.
  get isTopModal() {
    return false;
  }
  owns() {
    return this.masked ? true : super.owns(...arguments);
  }
  onDocumentMouseDown({ event }) {
    if (!event.isTrusted) {
      return;
    }
    if (!this.owns(event.target)) {
      this.hintFlow.stop();
    } else {
      super.onDocumentMouseDown(...arguments);
    }
  }
  changeTarget(target) {
    if (target) {
      if (typeof target === "string") {
        this._target = this._forElement = null;
      }
      target = this.hintFlow.normalizeTarget(target);
      if (ObjectHelper.isPromise(target)) {
        target.then((target2) => !this.isDestroyed && (this.target = target2));
        this.setTimeout("hide", this.maxWait);
        return;
      }
    }
    return target;
  }
  updateButtons(buttons) {
    this.bbar = {
      items: buttons
    };
  }
  updateHighlightTarget(highlightTarget, was) {
    const inflate = highlightTarget == null ? void 0 : highlightTarget.inflate;
    if (inflate) {
      this.initialInflate = this.align.inflate;
      this.align.inflate = inflate;
    } else {
      this.align.inflate = this.initialInflate;
    }
  }
  updateTarget(target) {
    if (target instanceof Element) {
      this.forElement = target;
    }
  }
  changeHighlighter(highlighter, was) {
    if (highlighter) {
      return Widget.reconfigure(was, Widget.mergeConfigs({
        owner: this
      }, highlighter), this);
    } else {
      was == null ? void 0 : was.destroy();
    }
  }
  async updateForElement(el) {
    const me = this, {
      autoNext,
      owner,
      index
    } = me;
    me.syncTargetHighlighting();
    me.clearTimeout("hide");
    me.hidden = false;
    me.showBy({ target: me.align.target || me.forElement });
    !me.element.matches(":focus-within") && me.focus();
    if (autoNext) {
      switch (typeof autoNext) {
        case "number":
          me.setTimeout(() => owner.navigate(1), autoNext);
          break;
        case "string":
          await AsyncHelper.sleep(500);
          if (me.index === index) {
            const target = await (me.autoNextPromise = owner.normalizeTarget(autoNext, false));
            me.autoNextPromise = null;
            if (target) {
              owner.navigate(1, false);
            }
          }
          break;
        case "function":
          await AsyncHelper.waitFor(autoNext, 1e4);
          owner.navigate(1, false);
      }
    }
  }
  syncTargetHighlighting() {
    if (!this.isVisible) {
      return;
    }
    const me = this, {
      highlightTarget,
      highlighter,
      forElement: el
    } = me, {
      classList: myClassList
    } = me.element, highlightStyles = {
      "--b-hint-highlight-color": ""
    }, element = highlighter == null ? void 0 : highlighter.element, highligherClassList = element == null ? void 0 : element.classList, inflate = highlightTarget == null ? void 0 : highlightTarget.inflate;
    highligherClassList == null ? void 0 : highligherClassList.remove("b-hint-highlighter-ping", "b-hint-highlighter-center");
    myClassList[me.scrollRealigning ? "add" : "remove"]("b-scroll-realigning");
    if (el) {
      const { style } = me.floatRoot, targetStyle = el.ownerDocument.defaultView.getComputedStyle(el), elRect = DomHelper.isInView(el, false, this), targetRect = (highlightTarget == null ? void 0 : highlightTarget.center) ? elRect.center.inflate(10) : elRect;
      if (inflate) {
        targetRect.inflate.apply(targetRect, Array.isArray(inflate) ? inflate : [inflate]);
      }
      if (highlightTarget && !(highlightTarget == null ? void 0 : highlightTarget.center)) {
        for (const t of ["top", "bottom"]) {
          for (const l of ["left", "right"]) {
            const r = targetStyle.getPropertyValue(`border-${t}-${l}-radius`);
            highlightStyles[`border-${t}-${l}-radius`] = parseInt(r) ? r : "";
          }
        }
      }
      if (typeof (highlightTarget == null ? void 0 : highlightTarget.center) === "string") {
        highlightStyles["--b-hint-highlight-color"] = highlightTarget.center;
      }
      style.setProperty("--b-highlight-top", `${targetRect.top}px`);
      style.setProperty("--b-highlight-left", `${targetRect.x}px`);
      style.setProperty("--b-highlight-width", `${targetRect.width}px`);
      style.setProperty("--b-highlight-height", `${targetRect.height}px`);
    }
    if (highlightTarget) {
      highligherClassList[me.scrollRealigning ? "add" : "remove"]("b-realigning");
      DomHelper.applyStyle(element, highlightStyles);
      highlighter.show();
      if (highlightTarget.ping) {
        EventHelper.onTransitionEnd({
          element,
          property: "inset-inline-start",
          once: true,
          thisObj: me,
          timeout: 500,
          handler: () => {
            highligherClassList.add("b-hint-highlighter-ping");
          }
        });
      }
      if (highlightTarget.center) {
        highligherClassList.add("b-hint-highlighter-center");
      }
    }
    me.requestAnimationFrame(() => {
      myClassList.remove("b-scroll-realigning");
      highligherClassList.remove("b-realigning");
    });
  }
  next() {
    if (this.index < this.owner.hints.length - 1) {
      this.owner.navigate(1);
    }
  }
  previous() {
    if (this.index) {
      this.owner.navigate(-1);
    }
  }
  stop() {
    this.owner.stop();
  }
};
//region Config
__publicField(Hint, "$name", "Hint");
__publicField(Hint, "type", "hint");
__publicField(Hint, "configurable", {
  /**
   * @hideconfigs defaultFocus, autoShow, autoClose, centered, allowOver, closable, closeAction
   */
  ui: "plain",
  defaultFocus: (w) => !w.hidden && (w.ref === "next" || w.ref === "stop"),
  align: {
    align: "t-b",
    allowTargetOut: true
  },
  anchor: true,
  autoShow: false,
  autoClose: false,
  centered: false,
  allowOver: true,
  closable: true,
  minWidth: "max-content",
  closeAction: "stop",
  /**
   * The actions to take when the 'Previous' button is clicked.
   *
   * By default, user interaction during these actions is disabled.
   *
   * The property names are the actions to take, and the values are the configuration for that action.
   *
   * @config {HintActions|HintActions[]} previousAction
   */
  previousAction: null,
  /**
   * The actions to take when the 'Next' button is clicked.
   *
   * By default, user interaction during these actions is disabled.
   *
   * The property names are the actions to take, and the values are the configuration for that action.
   *
   * @config {HintActions|HintActions[]} nextAction
   */
  nextAction: null,
  /**
   * The mapping of key names to hint actions.
   * The actions are `previous`, `next`, and `stop`.
   * By default, the following key mappings are used:
   * - `P` for `previous`
   * - `N` for `next`
   * - `S` for `stop`
   * The key mappings can be overridden by providing a `keyMap` config object.
   * @config {Object<String,'previous'|'next'|'stop'>}
   */
  keyMap: {
    P: "previous",
    N: "next",
    S: "stop"
  },
  /**
   * Overrides for buttons to show in the hint.
   *
   * By default, hints show a 'Next' button.
   *
   * The last hint will show a 'Previous' button instead of 'Next'.
   *
   * These buttons can be overridden by providing a `buttons` config object for the listed properties
   * @config {Object<String,ButtonConfig>} buttons
   * @prop {ButtonConfig} next Override the 'Next' button
   * @prop {ButtonConfig} previous Override the 'Previous' button
   */
  buttons: null,
  /**
   * How long in milliseconds to wait for a target to become available before giving up.
   * @config {Number}
   * @default
   */
  maxWait: 1e4,
  /**
   * Specify as a truthy value to highlight the target element with a bright outline.
   * @config {Boolean|Object}
   * @prop {Boolean} [ping] Specify as `true` to have the outline expand repeatedly with a "ping" animation.
   * @prop {String} [center] Specify as a CSS color to use as the the color of a 20 pixel diameter
   * @prop {Number|Number[]} inflate Arguments to {@link Core.helper.util.Rectangle#function-inflate}.
   * Specify as a number to inflate the target rect by that many pixels, or as an array of
   * inflate values for each edge.
   * @default false
   */
  highlightTarget: null,
  highlighter: {
    $config: ["lazy", "nullify"],
    value: {
      floating: true,
      cls: "b-hint-highlighter"
    }
  },
  /**
   * Either the number of milliseconds to wait before automatically moving to the next hint, or a CSS selector
   * to wait for before moving to the next hint.
   * @config {String|Number}
   */
  autoNext: null,
  /**
   * The target element to highlight, or a CSS selector to find the target element.
   * @config {String|HTMLElement}
   */
  target: null
});
Hint.initClass();
Hint._$name = "Hint";

// ../Core/lib/Core/util/HintFlow.js
var buttonSpec = (index, count) => {
  return {
    previous: {
      weight: 100,
      text: "L{Object.previous}",
      cls: `b-raised b-previous`,
      hidden: !index,
      onClick: "up.onHintButtonClick"
    },
    next: {
      weight: 100,
      text: "L{Object.next}",
      cls: "b-raised b-blue b-next",
      hidden: index === count - 1,
      onClick: "up.onHintButtonClick"
    },
    stop: {
      weight: 300,
      text: "L{Object.close}",
      cls: "b-raised b-red b-stop",
      hidden: index !== count - 1,
      onClick: "up.onHintButtonClick"
    }
  };
};
var keyExtractor = /\[(?:([\w+]+)\+)?(\w+)\]|./g;
var HintFlow = class extends Base.mixin(Localizable_default, Events_default, Delayable_default) {
  constructor() {
    super(...arguments);
    __publicField(this, "waitInterval", 100);
    __publicField(this, "maxWait", 1e4);
    __publicField(this, "keyStrokeIntervalRandomness", 100);
  }
  onHintButtonClick({ source }) {
    const me = this;
    switch (source.ref) {
      case "next":
        me.navigate(1);
        break;
      case "previous":
        me.navigate(-1);
        break;
      case "stop":
        me.stop();
        break;
      case "completed":
        me.trigger("completed");
        me.stop();
        break;
    }
  }
  async fakeType(options) {
    var _a5;
    let { target, text, initialDelay = 0, focus } = options;
    if (typeof options === "string") {
      target = document.activeElement;
      text = options;
    }
    if (focus !== false) {
      this.activeHint.revertFocus();
    }
    if (target) {
      target = await this.normalizeTarget(target);
      if (focus !== false) {
        target.focus();
      }
    } else {
      target = DomHelper.getActiveElement(this.activeHint.element);
    }
    if (options.clear) {
      target.value = "";
    }
    await AsyncHelper.sleep(initialDelay);
    for (let matches = keyExtractor.exec(text); matches; matches = keyExtractor.exec(text)) {
      if (matches[2]) {
        const modifiers = (((_a5 = matches[1]) == null ? void 0 : _a5.split("+")) || []).reduce((r, e) => {
          r[`${e.toLowerCase()}Key`] = true;
          return r;
        }, {}), keyEvent = { key: matches[2], bubbles: true, ...modifiers };
        target.dispatchEvent(new KeyboardEvent("keydown", keyEvent));
        target.dispatchEvent(new KeyboardEvent("keyup", keyEvent));
      } else {
        target.value += matches[0];
        target.dispatchEvent(new Event("input", { data: matches[0] }));
      }
      await AsyncHelper.sleep(Math.random() * this.keyStrokeIntervalRandomness + 30);
    }
    target.dispatchEvent(new Event("change"));
  }
  normalizeTarget(target, maxWait = this.maxWait) {
    return DomHelper.waitForSelector(target, maxWait, this.waitInterval);
  }
  navigate(direction, performAction = true) {
    if (this.navigationOngoing) {
      return;
    }
    this.navigationOngoing = new Promise(async (resolve) => {
      const me = this, { activeHint } = me, nextIndex = me.activeIndex + direction, l1 = GlobalEvents_default.ion({ mouseup: ({ event }) => me.squashDocumentEvents(event) }), l2 = EventHelper.on({
        element: document,
        mousedown: "squashDocumentEvents",
        keydown: "squashDocumentEvents",
        thisObj: me,
        capture: true
      });
      try {
        activeHint.masked = true;
        me.allowInteraction = false;
        if (performAction) {
          const promise = me.processActions(me.activeHint[direction === 1 ? "nextAction" : "previousAction"]);
          await AsyncHelper.animationFrame();
          if (me.normalizeTarget(activeHint.target, -1).nodeType !== 1) {
            const nextTarget = me.normalizeTarget(me.hints[nextIndex].target, -1);
            if ((nextTarget == null ? void 0 : nextTarget.nodeType) === 1) {
              activeHint.target = nextTarget;
            } else {
              DomHelper.applyStyle(activeHint.floatRoot, { "--b-highlight-width": "0px", "--b-highlight-height": "0px" });
            }
          }
          await promise;
        }
        me.activeIndex = nextIndex;
        await me.activeIndexUpdated;
        me.navigationOngoing = null;
        activeHint.masked = false;
      } finally {
        l1 == null ? void 0 : l1();
        l2 == null ? void 0 : l2();
        if (me.navigationOngoing) {
          me.navigationOngoing = null;
          activeHint.element.classList.remove("b-no-title");
          activeHint.hide(false);
          Hint.reconfigure(activeHint, {
            hidden: false,
            masked: false,
            centered: true,
            title: "Navigation timed out",
            content: "Navigation could not be completed.",
            nextAction: null,
            bbar: {
              items: buttonSpec(0, 1)
            }
          });
        }
        resolve();
      }
    });
  }
  squashDocumentEvents(event) {
    if (event.isTrusted) {
      if (this.activeHint.element.contains(event.target) || !this.allowInteraction) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }
  }
  async processActions(actions5) {
    var _a5;
    const me = this;
    if (actions5) {
      if (Array.isArray(actions5)) {
        for (const action of actions5) {
          await me.processActions(action);
        }
      } else {
        for (const [key, options] of Object.entries(actions5)) {
          switch (key) {
            case "type":
              await me.fakeType(options);
              break;
            case "dblclick":
            case "click":
            case "contextmenu":
              {
                const isString = typeof options === "string", target = await me.normalizeTarget(isString ? options : options.target), doMousedown = !isString && options.focus;
                if (doMousedown) {
                  const activeElement = DomHelper.getActiveElement(target), mousedown = DomHelper.triggerMouseEvent(target, "mousedown");
                  if (!mousedown.defaultPrevented && DomHelper.getActiveElement(target) === activeElement) {
                    (_a5 = DomHelper.closest(target, DomHelper.isFocusable)) == null ? void 0 : _a5.focus();
                  }
                }
                DomHelper.triggerMouseEvent(target, key);
                if (doMousedown) {
                  DomHelper.triggerMouseEvent(target, "mouseup");
                }
              }
              break;
            case "fn":
              await me.activeHint.callback(options, me.activeHint);
              break;
            case "drag":
              await me.drag(options);
              break;
            case "waitFor":
              if (isNaN(options)) {
                await me.normalizeTarget(options);
              } else {
                await AsyncHelper.sleep(options);
              }
              break;
            case "allowInteraction":
              me.allowInteraction = options;
              break;
          }
        }
      }
    }
  }
  async drag({ target = this.activeHint.target, by: [dx, dy], duration = 500 }) {
    const targetEl = await this.normalizeTarget(target), {
      ownerDocument: doc2
    } = targetEl, { activeHint } = this, { modalMask } = activeHint, { x, y } = Rectangle.from(targetEl).center.roundPx(1), pointer = DomHelper.createElement({
      parent: doc2.body,
      className: "b-mouse-pointer b-icon b-fa-mouse-pointer",
      style: {
        left: `${x}px`,
        top: `${y}px`
      }
    }), event = { clientX: x, clientY: y, bubbles: true }, { style } = activeHint.floatRoot;
    if (modalMask) {
      modalMask.style.pointerEvents = "none";
      style.setProperty("--b-highlight-width", "0px");
      style.setProperty("--b-highlight-height", "0px");
    }
    let overEl, lastOverEl;
    targetEl.dispatchEvent(new PointerEvent("pointerover", event));
    targetEl.dispatchEvent(new MouseEvent("mouseover", event));
    targetEl.dispatchEvent(new PointerEvent("pointerdown", event));
    targetEl.dispatchEvent(new MouseEvent("mousedown", event));
    await AsyncHelper.animationFrame();
    await FunctionHelper.animate(duration, async (progress) => {
      event.clientX = x + dx * progress;
      event.clientY = y + dy * progress;
      modalMask.style.pointerEvents = "none";
      overEl = doc2.elementFromPoint(event.clientX, event.clientY);
      pointer.style.cssText = `left:${event.clientX}px;top:${event.clientY}px`;
      overEl.dispatchEvent(new PointerEvent("pointermove", event));
      overEl.dispatchEvent(new MouseEvent("mousemove", event));
      if (overEl !== lastOverEl) {
        overEl.dispatchEvent(new PointerEvent("pointerenter", event));
        overEl.dispatchEvent(new MouseEvent("mouseenter", event));
        overEl.dispatchEvent(new PointerEvent("pointerover", event));
        overEl.dispatchEvent(new MouseEvent("mouseover", event));
        lastOverEl = overEl;
      }
      modalMask.style.pointerEvents = "";
    });
    style.setProperty("--b-highlight-top", `${event.clientY}px`);
    style.setProperty("--b-highlight-left", `${event.clientX}px`);
    overEl.dispatchEvent(new PointerEvent("pointerup", event));
    overEl.dispatchEvent(new MouseEvent("mouseup", event));
    pointer.remove();
  }
  async updateActiveIndex(index, was) {
    var _a5, _b2;
    const me = this, {
      hints,
      activeHint
    } = me, count = hints.length, cfg = hints[index];
    (_a5 = activeHint == null ? void 0 : activeHint.autoNextPromise) == null ? void 0 : _a5.resolve();
    if (cfg) {
      const target = await me.normalizeTarget(cfg.target);
      let { align } = cfg;
      if (typeof (align == null ? void 0 : align.target) === "string") {
        align = {
          ...align,
          target: await me.normalizeTarget(align.target)
        };
      }
      if (target === false) {
        me.activeIndex = ++index;
        return;
      }
      if (activeHint) {
        if (typeof cfg.target === "string") {
          activeHint._target = activeHint._forElement = null;
        }
        if (activeHint.bbar.element.matches(":focus-within")) {
          activeHint.element.focus();
        }
      } else {
        me.returnFocusTo = DomHelper.getActiveElement();
      }
      const buttons = {
        steps: {
          type: "widget",
          content: `${index + 1}/${count}`,
          weight: -1e3
        },
        completed: {
          text: me.L("L{Hint.dontShowAgain}"),
          cls: "b-transparent",
          weight: 10,
          onClick: "up.onHintButtonClick"
        },
        ...ObjectHelper.merge(buttonSpec(index, count), Array.isArray(cfg.buttons) ? ObjectHelper.transformArrayToNamedObject(cfg.buttons) : cfg.buttons)
      }, changedHint = me.activeHint = Hint.reconfigure(activeHint, Hint.mergeConfigs(me.defaults, {
        index,
        hintFlow: me,
        owner: me,
        maxWait: me.maxWait,
        rootElement: document.body
      }, cfg, {
        target,
        buttons,
        [typeof (align == null ? void 0 : align.target) === "string" ? "align" : "_"]: align
      }));
      me.owner = Hint.fromElement(target);
      changedHint.element.classList.toggle("b-no-title", Boolean(!cfg.title));
      await Promise.all([
        changedHint.highlighter.element.isConnected && DomHelper.waitForSelector(changedHint.highlighter.element, -1e3),
        DomHelper.waitForSelector(changedHint.element, -1e3)
      ]);
      if (changedHint.isVisible) {
        (_b2 = changedHint.bbar.widgetMap[index === count - 1 ? "stop" : index && index < was ? "previous" : "next"]) == null ? void 0 : _b2.focus();
      }
    } else {
      me.stop();
    }
  }
  async stop() {
    var _a5;
    const me = this;
    if (me.activeIndex === me.hints.length - 1) {
      await me.processActions(me.activeHint.nextAction);
    }
    me.activeHint.owner = null;
    bryntum.queryAll("hint").forEach((hint) => hint.hintFlow === me && hint.destroy());
    me.activeHint = null;
    (_a5 = me.returnFocusTo) == null ? void 0 : _a5.focus();
  }
};
__publicField(HintFlow, "configurable", {
  activeIndex: 0,
  /**
   * The array of hint configurations to show in the flow.
   * @config {HintConfig[]}
   */
  hints: null,
  /**
   * An object containing configuration properties to be applied to all hints in the flow.
   *
   * An example may be to set the `modal` property to `true` to make all hints modal,
   * or to set up keyboard shortcuts for the flow:
   *
   * ```javascript
   * new HintFlow({
   *     defaults : {
   *         modal  : true,
   *         keyMap : {
   *             'A' : 'next',
   *             'B' : 'previous',
   *             'C' : 'stop'
   *         }
   *     }
   * });
   * ```
   * @config {HintConfig} defaults
   */
  defaults: {
    type: "hint",
    align: {
      // Should not hide if its target goes out of view
      allowTargetOut: true
    },
    closable: true,
    draggable: false,
    buttons: {},
    autoNext: null,
    nextAction: null,
    previousAction: null,
    minWidth: null,
    trapFocus: true,
    // Property needs to be the empty string, but *not* falsy
    title: new String("")
  }
});
HintFlow._$name = "HintFlow";

// ../Core/lib/Core/util/Month.js
var _Month = class _Month extends Events_default(Base) {
  static get configurable() {
    return {
      /**
       * The date which the month should encapsulate. May be a `Date` object, or a
       * `YYYY-MM-DD` format string.
       *
       * Mutating a passed `Date` after initializing a `Month` object has no effect on
       * the `Month` object.
       * @config {Date|String}
       */
      date: {
        $config: {
          equal: "date"
        },
        value: DateHelper.clearTime(/* @__PURE__ */ new Date())
      },
      month: null,
      year: null,
      /**
       * The week start day, 0 meaning Sunday, 6 meaning Saturday.
       * Defaults to {@link Core.helper.DateHelper#property-weekStartDay-static}.
       * @config {Number}
       */
      weekStartDay: null,
      /**
       * Configure as `true` to have the visibleDayColumnIndex and visibleColumnCount properties
       * respect the configured {@link #config-nonWorkingDays}.
       * @config {Boolean}
       */
      hideNonWorkingDays: null,
      /**
       * Non-working days as an object where keys are day indices, 0-6 (Sunday-Saturday), and the value is `true`.
       * Defaults to {@link Core.helper.DateHelper#property-nonWorkingDays-static}.
       * @config {Object<String,Boolean>}
       */
      nonWorkingDays: null,
      /**
       * Configure as `true` to always have the month encapsulate six weeks.
       * This is useful for UIs which must be a fixed height.
       * @prp {Boolean}
       */
      sixWeeks: null
    };
  }
  //region events
  /**
   * Fired when setting the {@link #config-date} property causes the encapsulated date to change
   * in **any** way, date, week, month or year.
   * @event dateChange
   * @param {Core.util.Month} source The Month which triggered the event.
   * @param {Date} newDate The new encapsulated date value.
   * @param {Date} oldDate The previous encapsulated date value.
   * @param {Object} changes An object which contains properties which indicate what part of the date changed.
   * @param {Boolean} changes.d True if the date changed in any way.
   * @param {Boolean} changes.w True if the week changed (including same week in a different year).
   * @param {Boolean} changes.m True if the month changed (including same month in a different year).
   * @param {Boolean} changes.y True if the year changed.
   * @param {Boolean} changes.r True if the row count (with respect to the {@link #config-sixWeeks} setting) changed.
   */
  /**
   * Fired when setting the {@link #config-date} property causes a change of week. Note that
   * weeks are calculated in the ISO standard form such that if there are fewer than four
   * days in the first week of a year, then that week is owned by the previous year.
   *
   * The {@link #config-weekStartDay} is honoured when making this calculation and this is a
   * locale-specific value which defaults to the ISO standard of 1 (Monday) in provided European
   * locales and 0 (Sunday) in the provided US English locale.
   * @event weekChange
   * @param {Core.util.Month} source The Month which triggered the event.
   * @param {Date} newDate The new encapsulated date value.
   * @param {Date} oldDate The previous encapsulated date value.
   * @param {Object} changes An object which contains properties which indicate what part of the date changed.
   * @param {Boolean} changes.d True if the date changed in any way.
   * @param {Boolean} changes.w True if the week changed (including same week in a different year).
   * @param {Boolean} changes.m True if the month changed (including same month in a different year).
   * @param {Boolean} changes.y True if the year changed.
   * @param {Boolean} changes.r True if the row count (with respect to the {@link #config-sixWeeks} setting) changed.
   */
  /**
   * Fired when setting the {@link #config-date} property causes a change of month. This
   * will fire when changing to the same month in a different year.
   * @event monthChange
   * @param {Core.util.Month} source The Month which triggered the event.
   * @param {Date} newDate The new encapsulated date value.
   * @param {Date} oldDate The previous encapsulated date value.
   * @param {Object} changes An object which contains properties which indicate what part of the date changed.
   * @param {Boolean} changes.d True if the date changed in any way.
   * @param {Boolean} changes.w True if the week changed (including same week in a different year).
   * @param {Boolean} changes.m True if the month changed (including same month in a different year).
   * @param {Boolean} changes.y True if the year changed.
   * @param {Boolean} changes.r True if the row count (with respect to the {@link #config-sixWeeks} setting) changed.
   */
  /**
   * Fired when setting the {@link #config-date} property causes a change of year.
   * @event yearChange
   * @param {Core.util.Month} source The Month which triggered the event.
   * @param {Date} newDate The new encapsulated date value.
   * @param {Date} oldDate The previous encapsulated date value.
   * @param {Object} changes An object which contains properties which indicate what part of the date changed.
   * @param {Boolean} changes.d True if the date changed in any way.
   * @param {Boolean} changes.w True if the week changed (including same week in a different year).
   * @param {Boolean} changes.m True if the month changed (including same month in a different year).
   * @param {Boolean} changes.y True if the year changed.
   * @param {Boolean} changes.r True if the row count (with respect to the {@link #config-sixWeeks} setting) changed.
   */
  //endRegion
  /**
   * For use when this Month's `weekStartDay` is non-zero.
   *
   * An array to map the days of the week 0-6 of this Calendar to the canonical day numbers
   * used by the Javascript [Date](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date) object.
   * @member {Number[]} canonicalDayNumbers
   * @readonly
   */
  /**
   * An array to map a canonical day number to a *visible* column index.
   * For example, if we have `weekStartDay` as Monday which is 1, and non working days as
   * Wednesday, and `hideNonWorkingDays : true`, then the calendar would look like
   *
   * ```text
   * ┌────┬────┬────┬────┬────┬────┐
   * | Mo | Tu | Th | Fr | Sa | Su |
   * └────┴────┴────┴────┴────┴────┘
   * ```
   *
   * So we'd need this array: `[ 5, 0, 1, undefined, 2, 3, 4]`
   * @member {Number[]} visibleDayColumnIndex
   * @readonly
   */
  /**
   * An array to map a canonical day number to a 0-6 column index.
   * For example, if we have `weekStartDay` as Monday which is 1, then the calendar would look like
   *
   * ```text
   * ┌────┬────┬────┬────┬────┬────┬────┐
   * | Mo | Tu | We | Th | Fr | Sa | Su |
   * └────┴────┴────┴────┴────┴────┴────┘
   * ```
   *
   * So we'd need this array: `[ 6, 0, 1, 2, 3, 4, 5]`
   * @member {Number[]} dayColumnIndex
   * @readonly
   */
  /**
   * The number of visible days in the week as defined by the `nonWorkingDays` and
   * `hideNonWorkingDays` options.
   * @member {Number} weekLength
   * @readonly
   */
  configure(config) {
    super.configure(config);
    this.updateDayNumbers();
    if (config.date) {
      this.date = config.date;
    }
    this.generation = 0;
  }
  changeDate(date2) {
    if (this.isConfiguring) {
      return;
    }
    date2 = typeof date2 === "string" ? DateHelper.parse(date2, "YYYY-MM-DD") : new Date(date2);
    if (isNaN(date2)) {
      throw new Error("Month date ingestion must be passed a Date, or a valid YYYY-MM-DD date string");
    }
    return date2;
  }
  updateDate(newDate, oldDate) {
    const me = this, {
      dayColumnIndex,
      weekCount
    } = me, monthStart = DateHelper.getFirstDateOfMonth(newDate), monthEnd = DateHelper.getLastDateOfMonth(monthStart), startWeekDay = dayColumnIndex[monthStart.getDay()], endWeekDay = dayColumnIndex[monthEnd.getDay()], yearChanged = !oldDate || newDate.getFullYear() !== oldDate.getFullYear(), monthChanged = !oldDate || newDate.getMonth() !== oldDate.getMonth(), changes = me.eventListeners && (oldDate ? newDate.getDate() !== oldDate.getDate() | (me.getWeekId(newDate) !== me.getWeekId(oldDate)) << 1 | monthChanged << 2 | yearChanged << 3 : 15);
    me._year = newDate.getFullYear();
    me._month = newDate.getMonth();
    me.startDayOfMonth = 1 - startWeekDay;
    me.endDayOfMonth = monthEnd.getDate() + (6 - endWeekDay);
    if (me.sixWeeks) {
      me.endDayOfMonth += (6 - me.weekCount) * 7;
    }
    if (!me.weekBase || yearChanged) {
      me.calculateWeekBase();
    }
    if (monthChanged || yearChanged) {
      me.generation++;
    }
    if (changes) {
      const event = {
        newDate,
        oldDate,
        changes: {
          d: true,
          w: Boolean(changes & 2),
          m: Boolean(changes & 12),
          y: Boolean(changes & 8),
          r: me.weekCount !== weekCount
        }
      };
      me.trigger("dateChange", event);
      if (changes & 2) {
        me.trigger("weekChange", event);
      }
      if (changes & 12) {
        me.trigger("monthChange", event);
      }
      if (changes & 8) {
        me.trigger("yearChange", event);
      }
    }
  }
  calculateWeekBase() {
    const me = this, {
      dayColumnIndex
    } = me, jan1 = new Date(me.year, 0, 1), dec31 = new Date(me.year, 11, 31), january = me.month ? me.getOtherMonth(jan1) : me;
    if (me.dayColumnIndex[jan1.getDay()] > 3) {
      me.weekBase = january.startDate;
    } else {
      me.weekBase = new Date(me.year, 0, january.startDayOfMonth - 7);
    }
    const dec31Week = Math.floor(DateHelper.diff(me.weekBase, dec31, "day") / 7);
    me.has53weeks = dec31Week === 53 && dayColumnIndex[dec31.getDay()] > 2;
  }
  /**
   * Returns the week start date, based on the configured {@link #config-weekStartDay} of the
   * passed week.
   * @param {Number| Number[]} week The week number in the current year, or an array containing
   * `[year, weekOfYear]` for any year.
   *
   * Week numbers greater than the number of weeks in the year just wrap into the following year.
   */
  getWeekStart(week) {
    if (typeof week === "number") {
      return DateHelper.add(this.weekBase, Math.max(week, 1) * 7, "day");
    }
    const me = this, [year, weekOfYear] = week;
    if (year === me.year) {
      return me.getWeekStart(weekOfYear);
    }
    return me.getOtherMonth(new Date(year, 0, 1)).getWeekStart(weekOfYear);
  }
  getOtherMonth(date2) {
    const me = this, result = me === otherMonth ? new _Month(null) : otherMonth;
    result.configure({
      weekBase: null,
      weekStartDay: me.weekStartDay,
      nonWorkingDays: me.nonWorkingDays,
      hideNonWorkingDays: me.hideNonWorkingDays,
      sixWeeks: me.sixWeeks,
      date: new Date(date2.getFullYear(), 0, 1)
      // Make it easy to calculate its own weekBase
    });
    result.date = date2;
    result.updateDate(result.date, result.date);
    return result;
  }
  changeYear(year) {
    const newDate = new Date(this.date);
    newDate.setFullYear(year);
    this.date = newDate;
  }
  changeMonth(month2) {
    const newDate = new Date(this.date);
    newDate.setMonth(month2);
    this.date = newDate;
  }
  get weekStartDay() {
    return typeof this._weekStartDay === "number" ? this._weekStartDay : DateHelper.weekStartDay;
  }
  updateWeekStartDay() {
    const me = this;
    me.updateDayNumbers();
    if (!me.isConfiguring && me.date) {
      me.weekBase = null;
      me.updateDate(me.date, me.date);
    }
  }
  get nonWorkingDays() {
    return this._nonWorkingDays || DateHelper.nonWorkingDays;
  }
  changeNonWorkingDays(nonWorkingDays) {
    return ObjectHelper.assign({}, nonWorkingDays);
  }
  updateNonWorkingDays() {
    this.updateDayNumbers();
  }
  updateHideNonWorkingDays() {
    this.updateDayNumbers();
  }
  updateSixWeeks() {
    if (!this.isConfiguring) {
      this.updateDate(this.date, this.date);
    }
  }
  /**
   * The number of days in the calendar for this month. This will always be
   * a multiple of 7, because this represents the number of calendar cells
   * occupied by this month.
   * @property {Number}
   * @readonly
   */
  get dayCount() {
    return this.endDayOfMonth + 1 - this.startDayOfMonth;
  }
  /**
   * The number of weeks in the calendar for this month.
   * @property {Number}
   * @readonly
   */
  get weekCount() {
    return this.dayCount / 7;
  }
  /**
   * The date of the first cell in the calendar view of this month.
   * @property {Date}
   * @readonly
   */
  get startDate() {
    const me = this;
    if (me.year != null && me.month != null && me.startDayOfMonth != null) {
      return new Date(me.year, me.month, me.startDayOfMonth);
    }
  }
  /**
   * The date of the last cell in the calendar view of this month.
   * @property {Date}
   * @readonly
   */
  get endDate() {
    const me = this;
    if (me.year != null && me.month != null && me.startDayOfMonth != null) {
      return new Date(me.year, me.month, me.endDayOfMonth);
    }
  }
  /**
   * The first date of the current month.
   * @property {Date}
   * @readonly
   */
  get firstDate() {
    const me = this;
    if (me.year != null && me.month != null && me.startDayOfMonth != null) {
      return new Date(me.year, me.month, 1);
    }
  }
  /**
   * The last date of the current month.
   * @property {Date}
   * @readonly
   */
  get lastDate() {
    const me = this;
    if (me.year != null && me.month != null && me.startDayOfMonth != null) {
      return new Date(me.year, me.month + 1, 0);
    }
  }
  /**
   * Iterates through all calendar cells in this month, calling the passed function for each date.
   * @param {Function} fn The function to call.
   * @param {Date} fn.date The date for the cell.
   */
  eachDay(fn2) {
    for (let dayOfMonth = this.startDayOfMonth; dayOfMonth <= this.endDayOfMonth; dayOfMonth++) {
      fn2(new Date(this.year, this.month, dayOfMonth));
    }
  }
  /**
   * Iterates through all weeks in this month, calling the passed function
   * for each week.
   * @param {Function} fn The function to call.
   * @param {Number[]} fn.week An array containing `[year, weekNumber]`
   * @param {Date[]} fn.dates The dates for the week.
   */
  eachWeek(fn2) {
    const me = this, { weekCount } = me;
    for (let dayOfMonth = me.startDayOfMonth, week = 0; week < weekCount; week++) {
      const weekDates = [], weekOfYear = me.getWeekNumber(new Date(me.year, me.month, dayOfMonth));
      for (let day2 = 0; day2 < 7; day2++, dayOfMonth++) {
        weekDates.push(new Date(me.year, me.month, dayOfMonth));
      }
      fn2(weekOfYear, weekDates, week, week === weekCount - 1);
    }
  }
  /**
   * Returns the week of the year for the passed date. This returns an array containing *two* values,
   * the year **and** the week number are returned.
   *
   * The week number is calculated according to ISO rules, meaning that if the first week of the year
   * contains less than four days, it is considered to be the last week of the preceding year.
   *
   * The configured {@link #config-weekStartDay} is honoured in this calculation. So if the weekStartDay
   * is **NOT** the ISO standard of `1`, (Monday), then the weeks do not coincide with ISO weeks.
   * @param {Date} date The date to calculate the week for.
   * @returns {Number[]} A numeric array: `[year, week]`
   */
  getWeekNumber(date2) {
    const me = this;
    date2 = DateHelper.clearTime(date2);
    if (date2.getFullYear() !== me.year) {
      return me.getOtherMonth(new Date(date2.getFullYear(), 0, 1)).getWeekNumber(date2);
    }
    let weekNo = Math.floor(DateHelper.diff(me.weekBase, date2, "day") / 7), year = date2.getFullYear();
    if (!weekNo) {
      return me.getOtherMonth(new Date(me.year - 1, 0, 1)).getWeekNumber(new Date(me.year, 0, 0));
    } else if (weekNo === 53 && !me.has53weeks) {
      weekNo = 1;
      year++;
    } else if (weekNo > 53) {
      weekNo = weekNo % 52;
    }
    return [year, weekNo];
  }
  getWeekId(date2) {
    const week = this.getWeekNumber(date2);
    return week[0] * 100 + week[1];
  }
  getCellData(date2, ownerMonth, dayTime = DayTime.MIDNIGHT) {
    const me = this, day2 = date2.getDay(), visibleColumnIndex = me.visibleDayColumnIndex[day2], isNonWorking = me.nonWorkingDays[day2], isHiddenDay = me.hideNonWorkingDays && isNonWorking;
    if (date2 < me.startDate || date2 > me.endDate) {
      me.month = date2.getMonth();
    }
    return {
      day: day2,
      dayTime,
      visibleColumnIndex,
      isNonWorking,
      week: me.getOtherMonth(date2).getWeekNumber(date2),
      key: DateHelper.format(date2, "YYYY-MM-DD"),
      columnIndex: me.dayColumnIndex[day2],
      date: new Date(date2),
      dayEnd: dayTime.duration("s"),
      tomorrow: dayTime.dayOfDate(DateHelper.add(date2, 1, "day")),
      // These two properties are only significant when used by a CalendarPanel which encapsulates
      // a single month.
      isOtherMonth: Math.sign(date2.getMonth() + date2.getFullYear() * 12 - (ownerMonth.month + ownerMonth.year * 12)),
      visible: !isHiddenDay && (date2 >= ownerMonth.startDate && date2 < DateHelper.add(ownerMonth.endDate, 1, "day")),
      isRowStart: visibleColumnIndex === 0,
      isRowEnd: visibleColumnIndex === me.visibleColumnCount - 1
    };
  }
  updateDayNumbers() {
    const me = this, {
      weekStartDay,
      nonWorkingDays,
      hideNonWorkingDays
    } = me, dayColumnIndex = me.dayColumnIndex = [], canonicalDayNumbers = me.canonicalDayNumbers = [], visibleDayColumnIndex = me.visibleDayColumnIndex = [];
    let visibleColumnIndex = 0;
    for (let columnIndex = 0; columnIndex < 7; columnIndex++) {
      const canonicalDay = (weekStartDay + columnIndex) % 7;
      canonicalDayNumbers[columnIndex] = canonicalDay;
      dayColumnIndex[canonicalDay] = columnIndex;
      if (!hideNonWorkingDays || !nonWorkingDays[canonicalDay]) {
        visibleDayColumnIndex[canonicalDay] = visibleColumnIndex++;
      }
    }
    me.visibleColumnCount = visibleColumnIndex;
    me.weekLength = hideNonWorkingDays ? 7 - ObjectHelper.keys(nonWorkingDays).length : 7;
  }
};
__publicField(_Month, "$name", "Month");
var Month = _Month;
var otherMonth = new Month(null);
Month._$name = "Month";

// ../Core/lib/Core/util/Parser.js
var nextObjectIdentity = 0;
var objectIdentityMap = /* @__PURE__ */ new WeakMap();
var argsToCacheKey = (...args) => args.map((arg) => {
  let result;
  if (arg && typeof arg == "object" || typeof arg == "function") {
    result = objectIdentityMap.get(arg);
    if (result === void 0) {
      result = ++nextObjectIdentity;
      objectIdentityMap.set(arg, result);
    }
  } else {
    result = String(arg);
  }
  return result;
}).join("-");
var memo = (fn2) => {
  const mlist = /* @__PURE__ */ new Map();
  return (...args) => {
    const mkey = argsToCacheKey(args);
    let result = mlist.get(mkey);
    if (result === void 0) {
      result = fn2(...args);
      mlist.set(mkey, result);
    }
    return result;
  };
};
var memoCps = (fn2) => {
  const table = /* @__PURE__ */ new Map(), entryContinuations = (entry) => entry[0], entryResults = (entry) => entry[1], pushContinuation = (entry, cont) => entryContinuations(entry).push(cont), pushResult = (entry, result) => entryResults(entry).push(result), isResultSubsumed = (entry, result) => entryResults(entry).some((r) => ObjectHelper.isEqual(r, result)), makeEntry = () => [[], []], isEmptyEntry = (entry) => !entryResults(entry).length && !entryContinuations(entry).length, tableRef = (str) => {
    let entry = table.get(str);
    if (entry === void 0) {
      entry = makeEntry();
      table.set(str, entry);
    }
    return entry;
  };
  return (str, cont) => {
    const entry = tableRef(str);
    if (isEmptyEntry(entry)) {
      pushContinuation(entry, cont);
      fn2(str, (result) => {
        if (!isResultSubsumed(entry, result)) {
          pushResult(entry, result);
          entryContinuations(entry).forEach((cont2) => cont2(result));
        }
      });
    } else {
      pushContinuation(entry, cont);
      entryResults(entry).forEach((result) => cont(result));
    }
  };
};
var SUCCESS = Symbol("success");
var FAILURE = Symbol("failure");
var success = (val, rest) => [SUCCESS, val, rest];
var failure = (rest) => [FAILURE, rest];
var isSuccess = (result) => result.length && result[0] === SUCCESS;
var resolveParser = (p) => typeof p === "function" && !p.length ? p() : p;
var succeed = memo(
  (val) => memoCps(
    (str, cont) => cont(success(val, str))
  )
);
var string = memo(
  (match) => memoCps(
    (str, cont) => {
      const len = Math.min(match.length, str.length), head = str.substr(0, len), tail = str.substr(len);
      cont(head === match ? success(head, tail) : failure(tail));
    }
  )
);
var bind = (p, fn2) => (str, cont) => resolveParser(p)(str, (result) => {
  if (isSuccess(result)) {
    const [, val, rest] = result;
    fn2(val)(rest, cont);
  } else {
    cont(result);
  }
});
var seq = memo(
  (...parsers2) => {
    const seq2 = memo(
      (a, b) => memoCps(
        bind(
          a,
          (x) => bind(
            b,
            (y) => succeed([].concat(x, y))
          )
        )
      )
    );
    return parsers2.reduce(seq2, succeed([]));
  }
);
var alt = memo(
  (...parsers2) => memoCps(
    (str, cont) => parsers2.forEach((p) => resolveParser(p)(str, cont))
  )
);
var regexp = memo(
  (pattern) => (str, cont) => {
    const rexp = new RegExp(`^${pattern}`), match = rexp.exec(str);
    if (match) {
      const head = match[0], tail = str.substr(head.length);
      cont(success(head, tail));
    } else {
      cont(failure(str));
    }
  }
);
var red = memo(
  (p, fn2) => bind(
    p,
    (...val) => succeed(fn2(...[].concat.apply([], val)))
  )
);
var runParser = (body, str) => {
  const results = [];
  body(str, (result) => {
    if (isSuccess(result)) {
      const [, , left] = result;
      if (left === "") {
        results.push(result);
      }
    }
  });
  return results;
};
var defineParser = (body) => (str, cont) => cont ? resolveParser(body)(str, cont) : runParser(resolveParser(body), str);
var Parser_default = {
  memo,
  memoCps,
  success,
  failure,
  isSuccess,
  resolveParser,
  succeed,
  string,
  bind,
  seq,
  alt,
  regexp,
  red,
  runParser,
  defineParser
};

// ../Core/lib/Core/util/ScrollManager.js
var ScrollManager = class extends Base.mixin(Delayable_default, Events_default) {
  //region Default config
  static get configurable() {
    return {
      /**
       * Default element to use for scrolling. Can be overridden in calls to `startMonitoring()`.
       * @config {HTMLElement}
       */
      element: null,
      /**
       * Width in pixels of the area at the edges of an element where scrolling should be triggered
       * @config {Number}
       * @default
       */
      zoneWidth: 50,
      /**
       * Scroll speed, higher number is slower. Calculated as "distance from zone edge / scrollSpeed"
       * @config {Number}
       * @default
       */
      scrollSpeed: 5,
      /**
       * The direction(s) to scroll ('horizontal', 'vertical' or 'both')
       * @config {'horizontal'|'vertical'|'both'}
       * @default
       */
      direction: "both",
      /**
       * Number of milliseconds to wait before scroll starts when the mouse is moved close to an edge monitored by this scroll manager
       * @config {Number}
       * @default
       */
      startScrollDelay: 500,
      /**
       * Set to `true` to stop scrolling when pointing device leaves the scrollable element.
       * @config {Boolean}
       * @default
       */
      // https://github.com/bryntum/support/issues/394
      stopScrollWhenPointerOut: false,
      testConfig: {
        scrollSpeed: 2,
        startScrollDelay: 100
      },
      activeScroll: {
        $config: ["lazy"],
        value: {}
      },
      monitoring: {
        $config: ["lazy", "nullify"],
        value: true
      },
      owner: null
    };
  }
  changeMonitoring(value2, was) {
    was == null ? void 0 : was.clear();
    return /* @__PURE__ */ new Map();
  }
  //endregion
  doDestroy() {
    this.stopMonitoring();
    super.doDestroy();
  }
  /**
   * Returns true if some of the monitored elements is being scrolled at the moment.
   * @property {Boolean}
   * @readonly
   */
  get isScrolling() {
    return Object.keys(this.activeScroll).length !== 0;
  }
  get rtl() {
    var _a5;
    return (_a5 = this.owner) == null ? void 0 : _a5.rtl;
  }
  //region Start/stop monitoring
  /**
   * Starts monitoring an element. It will be scrolled if mouse is pressed and within `zoneWidth` pixels from element
   * edge. Supports monitoring multiple elements using `scrollables` option:
   *
   * ```javascript
   * new ScrollManager({ element : '.item' }).startMonitoring({
   *     scrollables : [
   *         {
   *             // Applies config to all elements matching `.item .child-item`
   *             // selector
   *             element : '.child-item',
   *             // Only manage vertical scroll
   *             direction : 'vertical',
   *             // Specific callback for this scrollable. Shared callback is
   *             // ignored.
   *             callback : () => console.log('Specific callback')
   *         },
   *         {
   *             // Instance can be used
   *             element : document.querySelector('.item .child2')
   *             // Direction and callback are not provided, so element will
   *             // be scrollable in horizontal direction and will use shared
   *             // callback
   *         }
   *     ],
   *     direction : 'horizontal',
   *     callback  : () => console.log('Shared callback')
   * })
   * ```
   *
   * @param {Object} config Element which might be scrolled or config { element, callback, thisObj }
   * @param {'horizontal'|'vertical'|'both'} [config.direction] Direction to scroll. Overrides default scroll direction
   * @param {Function} [config.callback] Callback to execute on every scroll of the target element.
   *
   * ```javascript
   * startMonitoring({
   *     callback(monitor) {
   *         // Current left and top scroll of the monitored element
   *         console.log(monitor.scrollLeft)
   *         console.log(monitor.scrollTop)
   *         // Scroll position relative to the initial position
   *         console.log(monitor.relativeScrollLeft)
   *         console.log(monitor.relativeScrollTop)
   *     }
   * })
   * ```
   *
   * @param {Object} [config.thisObj] Scope for the callback.
   * @param {Object[]} [config.scrollables] Array of configs if multiple elements should be monitored.
   * @param {HTMLElement|String} [config.scrollables.0.element] Element or selector.
   * @param {'horizontal'|'vertical'|'both'} [config.scrollables.0.direction] Direction to scroll. Overrides upper config object direction.
   * @param {Function} [config.scrollables.0.callback] Callback to execute on every scroll of the target element.
   * Overrides upper config object callback.
   * @returns {Function} Returns function to cleanup instantiated monitors
   * ```javascript
   * const detacher = new ScrollManager({ element }).startMonitoring({ ... });
   * detacher(); // All monitors setup by the previous call are removed
   * ```
   */
  startMonitoring(config = {}) {
    const me = this, {
      element,
      direction: defaultDirection
    } = me, {
      scrollables = [],
      direction = defaultDirection,
      callback
    } = config, attachedElements = [];
    if (!scrollables.length) {
      scrollables.push({ element });
    }
    scrollables.forEach((scrollable) => {
      const target = scrollable.element;
      if (typeof target === "string") {
        DomHelper.forEachSelector(element, target, (element2) => {
          me.createMonitor(element2, scrollable.direction || direction, scrollable.callback || callback);
          attachedElements.push(element2);
        });
      } else {
        me.createMonitor(target, scrollable.direction || direction, scrollable.callback || callback);
        attachedElements.push(target);
      }
    });
    return function detacher2() {
      var _a5;
      (_a5 = me.stopMonitoring) == null ? void 0 : _a5.call(me, attachedElements);
    };
  }
  createMonitor(element, direction, callback) {
    const { monitoring } = this;
    if (!monitoring.has(element)) {
      monitoring.set(element, new ScrollManagerMonitor({
        scrollManager: this,
        element,
        direction,
        callback
      }));
    }
  }
  /**
   * Stops monitoring an element. If no particular element is given, stop monitoring everything.
   * @param {HTMLElement|HTMLElement[]} [element] Element or array of elements for which monitoring is not desired any
   * more and should stop as soon as possible.
   */
  stopMonitoring(element) {
    const me = this, { monitoring } = me;
    element = ArrayHelper.asArray(element);
    if (monitoring) {
      if (!element) {
        monitoring.forEach((monitor) => me.stopMonitoring(monitor.element));
        return;
      }
      element.forEach((element2) => {
        const monitor = monitoring.get(element2);
        element2.classList.remove("b-scrolling");
        if (monitor) {
          monitor.destroy();
          monitoring.delete(element2);
        }
      });
    }
  }
  //endregion
  /*
   * Attempts to reserve given scrolling direction for the given monitor.
   * @param {String} direction 'horizontal' or 'vertical'
   * @param {Object} monitor
   * @returns {Boolean} Returns true in case scroll direction was reserved for given monitor. False otherwise.
   * @private
   */
  requestScroll(direction, monitor) {
    const { activeScroll } = this;
    if (direction in activeScroll && activeScroll[direction] !== monitor) {
      return false;
    } else {
      activeScroll[direction] = monitor;
      return true;
    }
  }
  /*
   * Releases all scroll directions, blocked by given monitor
   * @param {Object} monitor
   * @private
   */
  releaseScroll(monitor) {
    const { activeScroll = {} } = this;
    Object.keys(activeScroll).forEach((key) => {
      if (activeScroll[key] === monitor) {
        delete activeScroll[key];
      }
    });
  }
  //#region Scroll position
  getRelativeScroll(element, direction = "left") {
    let result = 0;
    this.monitoring.forEach((monitor, monitoredElement) => {
      if (DomHelper.isDescendant(monitoredElement, element)) {
        result += direction === "left" ? monitor.scrollRelativeLeft : monitor.scrollRelativeTop;
      }
    });
    return result;
  }
  //#endregion
};
var ScrollManagerMonitor = class extends Base {
  construct(config) {
    const me = this, { element } = config, startScrollLeft = element.scrollLeft, startScrollTop = element.scrollTop;
    Object.assign(config, { startScrollLeft, startScrollTop });
    super.construct(config);
    EventHelper.on({
      element,
      scroll: "onElementScroll",
      pointermove: "onMouseMove",
      // Capture pointermove events early to start scrolling from top elements
      capture: true,
      thisObj: me
    });
    EventHelper.on({
      element,
      pointerleave: "onPointerLeave",
      thisObj: me
    });
  }
  doDestroy() {
    this.stopScroll(true);
    super.doDestroy();
  }
  /**
   * Starts scrolling (see #performScroll). Called from onMouseMove.
   * @private
   */
  startScroll() {
    const me = this;
    if (me.pendingScrollFinalize) {
      me.scrollManager.releaseScroll(me);
      me.pendingScrollFinalize = false;
    }
    me.scrolling = true;
    me.performScroll();
  }
  /**
   * Stops scrolling. Called from onMouseMove.
   * @private
   */
  stopScroll(force = null) {
    const me = this, finalize = () => {
      me.pendingScrollFinalize = false;
      if (!me.isDestroyed) {
        me.scrollManager.releaseScroll(me);
        me.scrolling = false;
      }
    };
    if (me.scrollRequested) {
      me.scrollManager.cancelAnimationFrame(me.frameId);
      me.scrollRequested = false;
    }
    me.scrollManager.clearTimeout(me.scrollTimeout);
    me.scrollTimeout = null;
    if (!force && (me.ongoingScrollTop || me.ongoingScrollLeft)) {
      me.pendingScrollFinalize = true;
      Promise.all([me.ongoingScrollTop, me.ongoingScrollLeft].filter(Boolean)).then(() => me.pendingScrollFinalize && finalize());
    } else {
      me.ongoingScrollTop = me.ongoingScrollLeft = null;
      finalize();
    }
  }
  onPointerLeave() {
    this.scrollManager.stopScrollWhenPointerOut && this.stopScroll();
  }
  /**
   * Listener for mouse move on monitored element. Determines if scrolling is needed, and if so how fast to scroll.
   * See #zoneWidth & #scrollSpeed configs.
   * @private
   * @param {MouseEvent} event
   */
  onMouseMove(event) {
    const me = this, {
      scrollManager
    } = me, box = me.element.getBoundingClientRect(), width = scrollManager.zoneWidth, speed = scrollManager.scrollSpeed;
    me.scrollDeltaX = me.scrollDeltaY = 0;
    if (me.direction !== "vertical") {
      const { scrollLeft, scrollWidth, clientWidth } = me.element;
      if (scrollManager.rtl) {
        if (event.clientX < box.left + width && scrollWidth + scrollLeft - clientWidth >= 1) {
          me.scrollDeltaX = -Math.round((width + (box.left - event.clientX)) / speed) - 1;
        } else if (event.clientX > box.right - width && scrollLeft < 0) {
          me.scrollDeltaX = Math.round((width - (box.right - event.clientX)) / speed) + 1;
        }
      } else {
        if (event.clientX > box.right - width && scrollWidth - scrollLeft - clientWidth >= 1) {
          me.scrollDeltaX = Math.round((width - (box.right - event.clientX)) / speed) + 1;
        } else if (event.clientX < box.left + width && scrollLeft > 0) {
          me.scrollDeltaX = -Math.round((width + (box.left - event.clientX)) / speed) - 1;
        }
      }
    }
    if (me.direction !== "horizontal") {
      const { scrollTop, scrollHeight, clientHeight } = me.element;
      if (event.clientY > box.bottom - width && scrollHeight - scrollTop - clientHeight >= 1) {
        me.scrollDeltaY = Math.round((width - (box.bottom - event.clientY)) / speed) + 1;
      } else if (event.clientY < box.top + width && scrollTop > 0) {
        me.scrollDeltaY = -Math.round((width + (box.top - event.clientY)) / speed) - 1;
      }
    }
    if (me.scrollDeltaX !== 0 && !scrollManager.requestScroll("horizontal", me)) {
      me.scrollDeltaX = 0;
    }
    if (me.scrollDeltaY !== 0 && !scrollManager.requestScroll("vertical", me)) {
      me.scrollDeltaY = 0;
    }
    if (me.scrollDeltaX === 0 && me.scrollDeltaY === 0) {
      me.scrolling && me.stopScroll();
    } else if (!me.scrollTimeout) {
      me.scrollTimeout = scrollManager.setTimeout(() => me.startScroll(), scrollManager.startScrollDelay);
    }
  }
  /**
   * Scrolls by an amount determined by config.scrollDeltaX/Y on each frame. Start/stop by calling #startScroll and
   * #stopScroll.
   * @private
   */
  performScroll() {
    const me = this, { element, scrollManager } = me;
    if (me.scrolling && !me.scrollRequested) {
      if (me.scrollDeltaX !== 0) {
        const oldScrollLeft = element.scrollLeft, newScrollLeft = Math.min(oldScrollLeft + me.scrollDeltaX, element.scrollWidth - element.clientWidth);
        element.scrollLeft = newScrollLeft;
        if (element.scrollLeft !== oldScrollLeft) {
          me.ongoingScrollLeft = new Promise((resolve) => element.addEventListener("scroll", (event) => {
            var _a5;
            (_a5 = scrollManager.trigger) == null ? void 0 : _a5.call(scrollManager, "scroll", { event, deltaY: me.scrollDeltaY, deltaX: me.scrollDeltaX });
            resolve();
          }, { once: true }));
        } else {
          me.ongoingScrollLeft = null;
          me.scrollDeltaX = 0;
        }
      }
      if (me.scrollDeltaY !== 0) {
        const oldScrollTop = element.scrollTop, newScrollTop = Math.min(oldScrollTop + me.scrollDeltaY, element.scrollHeight - element.clientHeight);
        element.scrollTop = newScrollTop;
        if (element.scrollTop !== oldScrollTop) {
          me.ongoingScrollTop = new Promise((resolve) => element.addEventListener("scroll", (event) => {
            var _a5;
            (_a5 = scrollManager.trigger) == null ? void 0 : _a5.call(scrollManager, "scroll", { event, deltaY: me.scrollDeltaY, deltaX: me.scrollDeltaX });
            resolve();
          }, { once: true }));
        } else {
          me.ongoingScrollTop = null;
          me.scrollDeltaY = 0;
        }
      }
      if (me.scrollDeltaX !== 0 || me.scrollDeltaY !== 0) {
        me.scrollRequested = true;
        me.frameId = me.scrollManager.requestAnimationFrame(() => {
          me.scrollRequested = false;
          me.performScroll(me);
        });
      } else {
        me.stopScroll();
      }
    }
  }
  onElementScroll() {
    var _a5, _b2;
    (_b2 = (_a5 = this.config) == null ? void 0 : _a5.callback) == null ? void 0 : _b2.call(this.thisObj || this.scrollManager, this);
  }
  get scrollLeft() {
    return this.element.scrollLeft;
  }
  get scrollTop() {
    return this.verticalElement ? this.verticalElement.scrollTop : this.element.scrollTop;
  }
  get scrollRelativeLeft() {
    return this.scrollLeft - this.startScrollLeft;
  }
  get scrollRelativeTop() {
    return this.scrollTop - this.startScrollTop;
  }
  // There could be several scrollables controlling different axes. If we want to calculate combined scroll from all
  // those monitors (e.g. for the case when we drag scheduler event in both directions), we should ask scroll manager
  // to iterate over monitored elements and aggregate scroll.
  getRelativeLeftScroll(element) {
    return this.scrollManager.getRelativeScroll(element, "left");
  }
  getRelativeTopScroll(element) {
    return this.scrollManager.getRelativeScroll(element, "top");
  }
};
ScrollManager._$name = "ScrollManager";

// ../Core/lib/Core/widget/Tooltip.js
var realignTransitions = {
  left: true,
  right: true,
  top: true,
  bottom: true,
  transform: true
};
var isBoolean = {
  true: 1,
  false: 1
};
var immediatePromise5 = Promise.resolve();
var _Tooltip = class _Tooltip extends Popup {
  /**
   * Triggered before tooltip widget is shown. Return `false` to prevent the action.
   * @preventable
   * @async
   * @event beforeShow
   * @param {Core.widget.Tooltip} source The Tooltip
   */
  static get configurable() {
    return {
      /**
       * By default, a Tooltip is transient, and will {@link #function-hide} when the mouse exits the target
       * element. Configure as `false` to make a Tooltip non-transient.
       * @config {Boolean}
       * @default
       */
      autoHide: true,
      /**
       * By default, a Tooltip is transient, and will {@link #function-hide} when the user clicks or
       * taps outside its widget. Configure as `false` to make a Tooltip non-transient when user clicks outside it.
       *
       * If you would like the Tooltip to stay visible when mouse leaves the Tooltip target, please see
       * {#config-autoHide}.
       *
       * @config {Boolean} autoClose
       * @default true
       */
      /**
       * Horizontal offset from mouse when {@link #config-anchorToTarget} is `false`.
       *
       * Direction independent, the value is internally flipped (by multiplying it with -1) for RTL.
       *
       * @config {Number}
       * @default
       */
      mouseOffsetX: 15,
      /**
       * Vertical offset from mouse when {@link #config-anchorToTarget} is `false`
       * @config {Number}
       * @default
       */
      mouseOffsetY: 15,
      html: {
        // Ensure the html setter can never veto the operation as a no-change.
        // Because of beforeShow listeners augmenting the content.
        $config: {
          equals: () => false
        }
      },
      /**
       * A method, or the *name* of a method called to update the tooltip's content when the
       * cursor is moved over a target. It receives one argument containing context about the
       * tooltip and show operation. The function should return a string, or a Promise yielding
       * a string.
       *
       * ```javascript
       * new Grid({
       *     title    : 'Client list',
       *     appendTo : myElement,
       *     store    : myStore,
       *     columns  : myColumns,
       *     tbar     : {
       *         items : {
       *             text : 'Reload,
       *             tooltip : {
       *                 // Will look in ownership hierarchy for the method
       *                 // which will be found on the grid.
       *                 getHtml : 'up.getReloadButtonTip'
       *             }
       *         }
       *     },
       *     getReloadButtonTip() {
       *         return `Reload ${this.title}`;
       *     }
       * });
       * ```
       *
       * @config {Function|String}
       * @param {Object} context
       * @param {Core.widget.Tooltip} context.tip The tooltip instance
       * @param {HTMLElement} context.element The Element for which the Tooltip is monitoring mouse movement
       * @param {HTMLElement} context.activeTarget The target element that triggered the show
       * @param {Event} context.event The raw DOM event
       * @returns {String|Promise}
       */
      getHtml: null,
      /**
       * DOM element to attach tooltip to. By default, the mouse entering this element will kick off a timer
       * (see {@link #config-hoverDelay}) to show itself.
       *
       * If the {@link #config-forSelector} is specified, then mouse entering matching elements within the `forElement`
       * will trigger the show timer to start.
       *
       * Note that when moving from matching element to matching element within the `forElement`, the tooltip
       * will remain visible for {@link #config-hideDelay} milliseconds after exiting one element, so that rapidly
       * entering another matching element will not cause hide+show flicker. To prevent this behaviour configure
       * with `hideDelay: 0`.
       * @config {HTMLElement}
       */
      forElement: null,
      /**
       * By default, once a tooltip is shown aligned as requested, it stays put.
       *
       * Setting this to `true` causes the tooltip to be aligned by the mouse,
       * offset by `[{@link #config-mouseOffsetX}, {@link #config-mouseOffsetY}]` and
       * keeps the tooltip aligned to the mouse maintaining the configured offsets
       * as the mouse moves within its activating element.
       * @config {Boolean}
       * @default false
       */
      trackMouse: null,
      /**
       * By default, a tooltip displays static content. In the Scheduler however, there are
       * plenty of uses cases when the tip content is based on the current mouse position (dragging events, resizing events, schedule hover tip, drag creation of events etc). Set
       * to `true` to update contents on mouse movement.
       * @config {Boolean}
       * @private
       * @default
       */
      updateContentOnMouseMove: false,
      /**
       * A CSS selector which targets child elements of the {@link #config-forElement} that should produce a
       * tooltip when hovered over.
       * @config {String}
       */
      forSelector: null,
      /**
       * By default, when moving rapidly from target to target, if, when mouseovering
       * a new target, the tip is still visible, the tooltip does not hide, it remains
       * visible, but updates its content however it is configured to do so.
       *
       * Configure `hideOnDelegateChange : true` to have the tip hide, and then trigger
       * a new show delay upon entry of a new target while still visible.
       * @config {Boolean}
       * @default false
       */
      hideOnDelegateChange: null,
      /**
       * Set to `true` to anchor tooltip to the triggering target. If set to `false`, the tooltip
       * will align to the mouse position. When set to `false`, it will also set `anchor: false`
       * to hide anchor arrow.
       * @config {Boolean}
       * @default
       */
      anchorToTarget: true,
      /**
       * Show on hover
       * @config {Boolean}
       * @default false
       */
      showOnHover: null,
      /**
       * The amount of time to hover before showing
       * @prp {Number}
       * @default
       */
      hoverDelay: 500,
      /**
       * Show immediately when created
       * @config {Boolean}
       * @default
       */
      autoShow: false,
      /**
       * The time (in milliseconds) that the Tooltip should stay visible for when it shows over its
       * target. If the tooltip is anchored to its target, then moving the mouse during this time
       * resets the timer so that the tooltip will remain visible.
       *
       * Defaults to `0` which means the Tooltip will persist until the mouse leaves the target.
       * @config {Number}
       * @default
       */
      dismissDelay: 0,
      /**
       * The time (in milliseconds) for which the Tooltip remains visible when the mouse leaves the target.
       *
       * May be configured as `false` to persist visible after the mouse exits the target element. Configure it
       * as 0 to always retrigger `hoverDelay` even when moving mouse inside `fromElement`
       * @config {Number|Boolean}
       * @default
       */
      hideDelay: 500,
      /**
       * The message to show while an async tooltip is fetching its content.
       * @config {String}
       * @default
       */
      loadingMsg: "Loading...",
      /**
       * Keep the tooltip open if user moves the mouse over it.
       *
       * If this is *not* explicitly configured as `false`, then this is automatically set
       * when there are any visible, interactive child items added such as {@link #config-tools}, or
       * {@link #config-items} which are interactive such as buttons or input fields.
       * @config {Boolean}
       * @default false
       */
      allowOver: null,
      anchor: true,
      draggable: false,
      align: {
        align: "b-t",
        // This signals to the align code that this widget is prepared to shrink
        // in height in order to comply with alignTo specifications.
        // Without a minHeight, it is assumed that the height of the widget
        // is inviolable.
        minHeight: 300
      },
      axisLock: true,
      /**
       * The HTML element that triggered this Tooltip to show
       * @readonly
       * @member {HTMLElement} activeTarget
       */
      activeTarget: {
        $config: {
          // Any nullish is equal, so setting to null when undefined is no-change
          equal: (t1, t2) => t1 == null && t2 == null || t1 === t2
        },
        value: null
      },
      testConfig: {
        hideDelay: 100,
        hoverDelay: 100,
        showAnimation: null,
        hideAnimation: null
      },
      role: "tooltip",
      // Private for accessibility. If the activeTarget contains the current activeElement, the
      // describedElement is set to that activeElement while that activeTarget remains active.
      // This is so that it can have the aria-describedby property set to reference this tooltip.
      describedElement: null
    };
  }
  //endregion
  //region Events
  /**
   * Triggered when a mouseover event is detected on a potential target element.
   * Return false to prevent the action
   * @event pointerOver
   * @param {Core.widget.Tooltip} sourceThe tooltip instance.
   * @param {Event} event The mouseover event.
   * @preventable
   */
  //endregion
  //region Properties
  //endregion
  //region Init & destroy
  afterConfigure() {
    const me = this, { forSelector } = me;
    if (forSelector) {
      me.showOnHover = me.showOnHover !== false;
      if (!me.forElement) {
        if (!me.anchorToTarget) {
          me.trackMouse = true;
        }
        me.forElement = me.rootElement.host || me.rootElement;
      }
    }
    super.afterConfigure();
    if (me.forElement && me.showOnHover) {
      me.pointerOverOutDetacher = EventHelper.on({
        element: me.forElement,
        // Using pointerover/pointerout since mouseover events are not fired in Chrome when the native `disabled`
        // attribute is present https://github.com/bryntum/support/issues/3179
        pointerover: "internalOnPointerOver",
        pointerout: "internalOnPointerOut",
        thisObj: me
      });
    }
  }
  doDestroy() {
    var _a5;
    (_a5 = this.pointerOverOutDetacher) == null ? void 0 : _a5.call(this);
    super.doDestroy();
  }
  set focusOnToFront(focusOnToFront) {
    super.focusOnToFront = focusOnToFront;
  }
  get focusOnToFront() {
    var _a5;
    return ((_a5 = this.triggeredByEvent) == null ? void 0 : _a5.pointerType) === "focus" ? false : super.focusOnToFront && DomHelper.usingKeyboard;
  }
  get focusElement() {
    const result = super.focusElement;
    if (result !== this.element) {
      return result;
    }
  }
  get anchorToTarget() {
    return this._anchorToTarget && !this.trackMouse;
  }
  get anchor() {
    return super.anchor && !this.trackMouse;
  }
  set anchor(anchor) {
    super.anchor = anchor;
  }
  //endregion
  //region Hovering, show and hide
  onDocumentMouseDown({ event }) {
    var _a5;
    const me = this, { triggeredByEvent } = me;
    if (triggeredByEvent && DomHelper.isTouchEvent) {
      if (event.pageX === triggeredByEvent.pageX && event.pageY === triggeredByEvent.pageY && ((_a5 = me.activeTarget) == null ? void 0 : _a5.contains(event.target)) && performance.now() - triggeredByEvent.timeStamp < 500) {
        return;
      }
    }
    me.abortDelayedShow();
    super.onDocumentMouseDown({ event });
  }
  internalOnPointerOver(event) {
    const me = this, { target, relatedTarget } = event, { forElement, forSelector, activeTarget } = me;
    let newTarget;
    if (me.disabled || me.owner && !me.owner.showTooltipWhenDisabled && me.owner.disabled) {
      return;
    }
    if (me.allowOver && me.element.contains(target)) {
      return;
    }
    if (forSelector) {
      const autoTipElement = event.target.closest("[data-btip]");
      if (autoTipElement && !autoTipElement.matches(forSelector)) {
        if (activeTarget) {
          me.handleForElementOut();
        }
        return;
      }
      ;
      newTarget = me.filterTarget(event);
      if (!this.hasTimeout("hide") && (activeTarget == null ? void 0 : activeTarget.contains(target)) && activeTarget.contains(relatedTarget) && newTarget === activeTarget) {
        return;
      }
      if (newTarget && (relatedTarget == null ? void 0 : relatedTarget.closest(forSelector)) === newTarget && !relatedTarget.dataset.btip) {
        return;
      }
    } else if (!forElement.contains(relatedTarget)) {
      newTarget = forElement;
    } else {
      return;
    }
    if (newTarget) {
      me.handleForElementOver(event, newTarget);
    } else if (activeTarget) {
      me.handleForElementOut();
    }
  }
  filterTarget({ target }) {
    return target.closest(this.forSelector);
  }
  // Handle a transitioned reposition when the activeTarget moved beneath the pointer.
  // When it comes to an end, if the mouseout has not hidden, then realign at the new position
  // if the activeTarget is still beneath the pointer.
  realignOnTransitionEnd(event) {
    var _a5;
    const me = this, { currentOverElement: currentOverElement2 } = _Tooltip;
    if (realignTransitions[event.propertyName]) {
      if (me.allowOver && me.element.contains(currentOverElement2)) {
        return;
      }
      if (((_a5 = me.activeTarget) == null ? void 0 : _a5.contains(currentOverElement2)) && !me.trackMouse) {
        me.realign();
      }
    }
  }
  async handleForElementOver(event, newTarget) {
    const me = this, {
      activeTarget,
      hideOnDelegateChange,
      anchorToTarget
    } = me, isNewTarget = newTarget !== activeTarget, needsHide = isNewTarget && hideOnDelegateChange;
    if (me.trigger("pointerOver", { event, target: newTarget }) === false) {
      me.internalOnPointerOut(event);
    } else {
      me.triggeredByEvent = event;
      if (me.hasTimeout("hide")) {
        me.abortDelayedHide();
        if (!isNewTarget) {
          return;
        }
      }
      if (!hideOnDelegateChange && me.element.classList.contains("b-hiding")) {
        me.cancelHideShowAnimation();
      }
      if (!me._hidden && needsHide) {
        me.hide(false);
      }
      if (me.activeTarget && me.hasTimeout("show")) {
        me.clearTimeout("show");
      }
      me.activeTarget = newTarget;
      if (me.activeTarget !== newTarget) {
        me.internalOnPointerOut(event);
        return;
      }
      if (me.isVisible) {
        me.lastAlignSpec = null;
        const result = me.updateContent();
        if (Objects.isPromise(result) && !me.loadingMsg) {
          await result;
        }
        if (me.trigger("beforeShow") === false) {
          return me.hide();
        }
        me.alignTo({
          [anchorToTarget ? "target" : "position"]: anchorToTarget ? newTarget : "mouse",
          overlap: !(anchorToTarget && me.anchor)
        });
        me.trigger("show");
        me.afterShowByTarget();
      } else {
        me.activeTarget = newTarget;
        me.delayShow(newTarget);
      }
    }
  }
  async delayShow(target) {
    var _a5;
    const me = this;
    if ((_a5 = me.currentAnimation) == null ? void 0 : _a5.showing) {
      me.cancelHideShowAnimation();
    }
    if (!me.isVisible && !me.hasTimeout("show")) {
      if (!me.hoverDelay || me.forSelector && Date.now() - me.lastHidden < me.quickShowInterval) {
        const result = me.updateContent();
        if (Objects.isPromise(result) && !me.loadingMsg) {
          await result;
        }
        me.showByTarget(target);
      } else {
        me.addDocumentMouseDownListener();
        if (!me.listeningForMouseMove && !me.anchorToTarget) {
          me.mouseMoveRemover = EventHelper.on({
            element: me.rootElement,
            mousemove: "onMouseMove",
            thisObj: me
          });
        }
        me.setTimeout(async () => {
          if (me.activeTarget === target && target.isConnected) {
            const result = me.updateContent();
            if (Objects.isPromise(result) && !me.loadingMsg) {
              await result;
            }
            me.showByTarget(target);
          }
        }, !me.triggeredByEvent || me.triggeredByEvent.type === "pointerover" ? me.hoverDelay : 0, "show");
      }
    } else if (me.isVisible) {
      me.showByTarget(target);
    }
  }
  changeAllowOver(allowOver) {
    if (!this.inAfterShow) {
      this.configuredAllowOver = allowOver;
    }
    return allowOver;
  }
  updateAllowOver(allowOver) {
    var _a5;
    const me = this, { element } = me;
    element.classList.toggle("b-allow-over", Boolean(allowOver));
    if (allowOver) {
      me.allowOverlisteners = EventHelper.on({
        element,
        mouseenter: "onOwnElementMouseEnter",
        mouseleave: "internalOnPointerOut",
        thisObj: me
      });
    } else {
      (_a5 = me.allowOverlisteners) == null ? void 0 : _a5.call(me);
    }
  }
  updateContent() {
    const me = this;
    if (me.getHtml) {
      const result = me.callback(me.getHtml, me, [{
        tip: me,
        element: me.element,
        activeTarget: me.activeTarget,
        event: me.triggeredByEvent
      }]);
      me.html = result;
      return result;
    }
  }
  // There are 3 possible scenarios:
  // - Static content
  // - Remote content being loaded (meaning we (possibly) set a loading message as the `html`
  // - Tooltip acts as a Container
  get hasContent() {
    return Boolean(
      DomHelper.isVueConfig(this._html) || DomHelper.isReactElement(this._html) || (this._html !== "" && (typeof this.html === "string" && this.html.length) || this.items.length)
    );
  }
  internalBeforeShow() {
    const me = this;
    if (me.isSharedTooltip && DragHelper.dragging) {
      return false;
    }
    return (me.updateContentOnMouseMove || me.hasContent) && !me.disabled;
  }
  /**
   * Shows a spinner and a message to indicate an async flow is ongoing
   * @param {String} message The message, defaults to {@link #config-loadingMsg}
   */
  showAsyncMessage(message = this.optionalL(this.loadingMsg)) {
    if (message) {
      this.html = `
                <div class="b-tooltip-loading">
                    <i class="b-icon b-icon-spinner"></i>
                    ${StringHelper.encodeHtml(message)}
                </div>
            `;
    }
  }
  showByTarget(target) {
    var _a5;
    const me = this, { anchorToTarget } = me;
    me.mouseMoveRemover = (_a5 = me.mouseMoveRemover) == null ? void 0 : _a5.call(me);
    me.showBy({
      [anchorToTarget ? "target" : "position"]: anchorToTarget ? target : "mouse",
      overlap: !(anchorToTarget && me.anchor)
    });
  }
  afterShowByTarget() {
    const me = this, { dismissDelay, activeTarget } = me, activeElement = DomHelper.getActiveElement(me.activeTarget);
    me.abortDelayedShow();
    if (dismissDelay) {
      me.setTimeout("hide", dismissDelay);
    }
    if (me.element.parentNode) {
      me.toFront();
    }
    if (!me.mouseMoveRemover && (me.trackMouse || me.updateContentOnMouseMove)) {
      me.mouseMoveRemover = EventHelper.on({
        element: me.rootElement,
        pointermove: "onMouseMove",
        thisObj: me
      });
    }
    me.describedElement = (activeTarget == null ? void 0 : activeTarget.contains(activeElement)) ? activeElement : null;
    me.inAfterShow = true;
    me.allowOver = me.allowOver || me.configuredAllowOver != false && me.childItems.some((w) => w.isVisible && !w.disabled && w.focusElement);
    me.inAfterShow = false;
  }
  changeActiveTarget(newTarget, lastTarget) {
    const me = this;
    if (newTarget) {
      if (!me.isConfiguring && me.trigger("overTarget", { newTarget, lastTarget }) === false) {
        return;
      }
    } else {
      me.trigger("targetOut", { lastTarget });
    }
    return newTarget;
  }
  updateDescribedElement(describedElement, oldDescribedElement) {
    const { titleElement } = this, titleId = titleElement && DomHelper.getId(titleElement), id2 = DomHelper.getId(this.contentElement);
    if (describedElement) {
      if (describedElement.getAttribute("aria-label") !== this.html) {
        titleId && DomHelper.addAttributeValue(describedElement, "aria-describedby", titleId);
        DomHelper.addAttributeValue(describedElement, "aria-describedby", id2);
      }
    }
    if (oldDescribedElement) {
      titleId && DomHelper.removeAttributeValue(oldDescribedElement, "aria-describedby", titleId);
      DomHelper.removeAttributeValue(oldDescribedElement, "aria-describedby", id2);
    }
  }
  internalOnPointerOut(event) {
    var _a5;
    const me = this, toElement = event.type === "pointerover" ? event.target : event.relatedTarget;
    if (me.allowOver && (me.element.contains(toElement) || me.owns(toElement, true))) {
      return;
    }
    if (me.element.contains(toElement) && ((_a5 = me.activeTarget) == null ? void 0 : _a5.contains(toElement))) {
      return;
    }
    if (me.activeTarget && !me.activeTarget.contains(toElement)) {
      me.handleForElementOut();
    }
  }
  handleForElementOut() {
    const me = this, { hideDelay } = me;
    if (me.trigger("pointerOut") === false) {
      me.activeTarget = null;
      return true;
    }
    me.abortDelayedShow();
    if (me.isVisible && hideDelay !== false) {
      me.abortDelayedHide();
      if (hideDelay > 0) {
        me.setTimeout("hide", hideDelay);
      } else {
        me.hide();
      }
    }
  }
  show(spec) {
    const me = this;
    if (!spec && me.forElement && me.anchorToTarget && !me.forSelector) {
      me.showByTarget(me.forElement);
    } else {
      super.show(...arguments);
    }
    if (me.isVisible) {
      me.afterShowByTarget();
    } else {
      me.activeTarget = null;
    }
  }
  get hoverDelay() {
    var _a5;
    return ((_a5 = this.triggeredByEvent) == null ? void 0 : _a5.pointerType) === "focus" ? 0 : this._hoverDelay;
  }
  get hideDelay() {
    var _a5;
    return ((_a5 = this.triggeredByEvent) == null ? void 0 : _a5.pointerType) === "focus" ? 0 : this.autoHide ? this._hideDelay : false;
  }
  hide() {
    var _a5, _b2;
    const me = this;
    me.abortDelayedShow();
    if (!me._hidden) {
      me.abortDelayedHide();
      const result = super.hide(...arguments);
      me.lastHidden = Date.now();
      me.activeTarget = null;
      (_a5 = me.mouseMoveRemover) == null ? void 0 : _a5.call(me);
      me.mouseMoveRemover = null;
      (_b2 = me.transitionEndDetacher) == null ? void 0 : _b2.call(me);
      me.transitionEndDetacher = null;
      me.describedElement = null;
      return result;
    } else {
      return immediatePromise5;
    }
  }
  abortDelayedShow() {
    var _a5, _b2, _c2;
    const me = this;
    if (me.hasTimeout("show")) {
      (_a5 = me.mouseDownRemover) == null ? void 0 : _a5.call(me);
      me.mouseDownRemover = null;
      me.clearTimeout("show");
      (_b2 = me.mouseMoveRemover) == null ? void 0 : _b2.call(me);
      me.mouseMoveRemover = null;
      (_c2 = me.transitionEndDetacher) == null ? void 0 : _c2.call(me);
      me.transitionEndDetacher = null;
    }
  }
  /**
   * Stops both timers which may hide this tooltip, the one which counts down from mouseout
   * and the one which counts down from mouseover show for dismissDelay ms
   * @private
   */
  abortDelayedHide() {
    this.clearTimeout("hide");
  }
  realign() {
    const me = this, spec = me.lastAlignSpec;
    if (!me.isConfiguring && !me.isVisible && (spec == null ? void 0 : spec.targetOutOfView)) {
      if (spec.allowTargetOut || DomHelper.isInView(spec.target, false, me)) {
        me.show();
        spec.targetOutOfView = false;
      }
    }
    super.realign();
  }
  alignTo(spec) {
    const me = this, mouseOffsetX = (me.mouseOffsetX || 1) * (me.rtl ? -1 : 1), mouseOffsetY = me.mouseOffsetY || 1, xMargin = Math.max(Math.min(mouseOffsetX, 5), 0), yMargin = Math.max(Math.min(mouseOffsetY, 5), 0);
    if (!me.isVisible) {
      return;
    }
    let mousePosition;
    if (!me.anchorToTarget && spec.position === "mouse") {
      mousePosition = new Point(
        me.triggeredByEvent.pageX - globalThis.scrollX,
        me.triggeredByEvent.pageY - globalThis.scrollY
      );
      spec.position = new Point(
        mousePosition.x + (mouseOffsetX || 1) - (mouseOffsetX < 0 ? me.width : 0),
        mousePosition.y + (mouseOffsetY || 1) - (mouseOffsetY < 0 ? me.height : 0)
      );
    }
    if (spec && !(me.triggeredByEvent && me.element.contains(me.triggeredByEvent.target))) {
      if (spec.nodeType === Node.ELEMENT_NODE) {
        spec = {
          target: spec
        };
      }
    }
    super.alignTo(spec);
    if (mousePosition && me.lastAlignSpec.result.inflate(yMargin, xMargin).contains(mousePosition)) {
      me.lastAlignSpec.position = "mouse";
      me.mouseOffsetY = -mouseOffsetY;
      me.mouseOffsetX = -mouseOffsetX;
      me.realign();
      me.mouseOffsetY = mouseOffsetY;
      me.mouseOffsetX = mouseOffsetX;
    }
  }
  //endregion
  //region Tooltip contents
  /**
   * Get/set the HTML to display. When specifying HTML, this widget's element will also have `b-html` added to its
   * classList, to allow targeted styling. To create async tooltip and show the {@link #config-loadingMsg}, see code below:
   * For example:
   *
   * ```javascript
   * new Tooltip({
   *     listeners : {
   *         beforeShow : ({ source : tip }) => {
   *             tip.showAsyncMessage();
   *             AjaxHelper.get('someurl').then(response => tip.html = 'Done!');
   *         }
   *     }
   * });
   * ```
   *
   * @member {String} html
   * @category DOM
   */
  set html(html) {
    super.html = html;
    if (this.isVisible) {
      this.realign();
    }
  }
  get html() {
    return super.html;
  }
  changeHtml(htmlOrPromise) {
    const me = this;
    if (Objects.isPromise(htmlOrPromise)) {
      me.showAsyncMessage();
      htmlOrPromise.target = me.activeTarget;
      htmlOrPromise.then((html) => {
        if (htmlOrPromise.target === me.activeTarget) {
          me.html = html;
        }
      });
      return;
    }
    if (typeof htmlOrPromise !== "object") {
      htmlOrPromise = htmlOrPromise != null ? me.optionalL(String(htmlOrPromise)) : "";
    }
    return htmlOrPromise;
  }
  updateHtml(value2, was) {
    const me = this;
    let empty2 = value2 === "";
    if (!empty2 || DomHelper.isVueConfig(value2)) {
      super.updateHtml(value2, was);
      if (me.hasContent) {
        if (!Objects.isPromise(value2)) {
          me.trigger("innerHtmlUpdate", { value: value2 });
        }
      } else {
        empty2 = true;
      }
    }
    if (empty2) {
      me.hide();
    }
  }
  //endregion
  //region Events
  /**
   * Mouse move event listener which updates tooltip
   * @private
   */
  onMouseMove(event) {
    const me = this, {
      forElement,
      activeTarget
    } = me, isHiding = me.hasTimeout("hide"), target = event.target;
    me.triggeredByEvent = event;
    if (!me._hidden) {
      let hideVetoed;
      const isWithinTarget = activeTarget == null ? void 0 : activeTarget.contains(target), isElementOut = !isWithinTarget && me.forSelector && activeTarget && !isHiding && target.nodeType === Node.ELEMENT_NODE && !target.matches(me.forSelector) && !(me.allowOver && me.element.contains(target)), containingElement = (forElement == null ? void 0 : forElement.document) ? forElement.document.documentElement : forElement, forElementContainsTarget = containingElement && (containingElement.contains ? containingElement.contains(target) : containingElement.compareDocumentPosition(target) & 16);
      if (isElementOut) {
        hideVetoed = me.handleForElementOut();
      }
      if (hideVetoed || !isHiding || forElementContainsTarget) {
        if (me.dismissDelay && !isHiding) {
          me.setTimeout("hide", me.dismissDelay);
        }
        if (me.updateContentOnMouseMove && me.getHtml) {
          me.html = me.callback(me.getHtml, me, [{
            tip: me,
            element: me.element,
            forElement: activeTarget,
            activeTarget,
            event
          }]);
          if (!me.html) {
            me.hide();
            return;
          }
        }
        if (me.trackMouse) {
          me.alignTo({
            position: "mouse",
            ignorePageScroll: true
          });
        }
      }
    }
  }
  // Handle if implementer has defined "showOnClick"
  onElementUserAction(event) {
    const { showOnClick } = this;
    if (typeof showOnClick !== "number" || showOnClick === event.button) {
      if (event.button === 2) {
        event.preventDefault();
      }
      this.internalOnPointerOver(event);
    }
  }
  onOwnElementMouseEnter(event) {
    this.abortDelayedHide();
  }
  //endregion
  // rootElement = where to find the float root
  // forElement = where to set up listeners
  // Can be different when using a shadowRoot not part of a webcomponent
  static getSharedTooltip(rootElement, forElement, skipCreating) {
    var _a5, _b2, _c2;
    let me = (_b2 = (_a5 = forElement.bryntum) == null ? void 0 : _a5.tooltip) == null ? void 0 : _b2.get(_Tooltip);
    if (!me && !skipCreating) {
      if (!((_c2 = forElement.bryntum) == null ? void 0 : _c2.tooltip)) {
        ObjectHelper.setPath(forElement, "bryntum.tooltip", /* @__PURE__ */ new Map());
      }
      const map2 = forElement.bryntum.tooltip;
      map2.set(_Tooltip, true);
      me = new _Tooltip({
        forElement,
        rootElement,
        forSelector: "[data-btip]",
        resetCfg: {},
        isSharedTooltip: true,
        cls: "b-tooltip-shared",
        internalListeners: {
          // Reconfigure on pointerOver
          pointerOver({ target }) {
            if (me.isOverflowDetail) {
              return;
            }
            for (const key in me.resetCfg) {
              if (key === "listeners") {
                me.un(me.resetCfg[key].set);
              } else if (key !== "html") {
                me[key] = me.resetCfg[key].was;
              }
            }
            me.resetCfg = {};
            const forComponent = Widget.getById(target.id), config = (forComponent == null ? void 0 : forComponent.tipConfig) || me.gatherDataConfigs(target);
            if (forComponent && forComponent.element !== target || !forComponent && target.matches(".b-widget") || // Respect our forComponent's wish to not show when it's disabled
            (forComponent == null ? void 0 : forComponent.disabled) && !forComponent.showTooltipWhenDisabled) {
              return false;
            }
            me._owner = forComponent;
            me.element.classList.toggle("b-target-disabled", Boolean(forComponent == null ? void 0 : forComponent.disabled));
            for (const key in config) {
              me.resetCfg[key] = {
                set: config[key],
                was: me[key]
              };
              if (key === "listeners") {
                me.ion(config[key]);
              } else {
                me[key] = config[key];
              }
            }
          },
          hide({ source: me2 }) {
            me2.owner = null;
          }
        },
        gatherDataConfigs(target) {
          const { dataset } = target, config = {};
          for (const key in dataset) {
            if (key.startsWith("btip")) {
              if (key.length > 4) {
                const configProp = StringHelper.uncapitalize(key.substr(4));
                if (configProp in me.getDefaultConfiguration()) {
                  const value2 = dataset[key];
                  config[configProp] = isBoolean[value2] ? value2 === "true" : isNaN(value2) ? value2 : parseInt(value2, 10);
                }
              } else {
                config.html = dataset[key];
              }
            }
          }
          if (target.hasAttribute("aria-label") && !config.html) {
            config.html = StringHelper.encodeHtml(me.optionalL(target.getAttribute("aria-label")));
          } else if (target.hasAttribute("aria-labelledby")) {
            config.html = StringHelper.encodeHtml(me.optionalL(document.getElementById(target.getAttribute("aria-labelledby").textContent)));
          }
          return config;
        },
        filterTarget({ target: element }) {
          me.isOverflowDetail = false;
          const target = element.closest(me.forSelector);
          if (target) {
            return target;
          }
          if (_Tooltip.showOverflow && element.closest(".b-widget")) {
            while ((element == null ? void 0 : element.nodeType) === Element.ELEMENT_NODE) {
              if (DomHelper.getStyleValue(element, "text-overflow") === "ellipsis" && element.clientWidth < element.scrollWidth) {
                me.html = StringHelper.encodeHtml(element.textContent);
                me.isOverflowDetail = true;
                return element;
              }
              element = element.parentNode;
            }
          }
        }
      });
      EventHelper.on({
        element: forElement,
        mouseenter: (event) => _Tooltip.currentOverElement = event.target,
        // If mouse is not used for editing cell then Tooltip has no `currentOverElement` and no error tooltip is shown. We use keydown event.target for this
        keydown: (event) => _Tooltip.currentOverElement = event.target,
        capture: true,
        thisObj: me
      });
      map2.set(_Tooltip, me);
    }
    return me;
  }
  static encodeConfig(tooltip) {
    const dataset = {};
    if (typeof tooltip === "string") {
      dataset.btip = tooltip;
    } else {
      for (const config in tooltip) {
        dataset[`btip${config === "html" ? "" : StringHelper.capitalize(config)}`] = tooltip[config];
      }
    }
    return dataset;
  }
};
//region Default config
__publicField(_Tooltip, "$name", "Tooltip");
__publicField(_Tooltip, "type", "tooltip");
var Tooltip = _Tooltip;
Tooltip.initClass();
Object.defineProperty(Widget, "tooltip", {
  get() {
    return Tooltip.getSharedTooltip(document.body, document.body);
  }
});
Widget.Tooltip = Tooltip;
Tooltip._$name = "Tooltip";

// ../Core/lib/Core/util/drag/DragTipProxy.js
var DragTipProxy = class extends DragProxy {
  static get configurable() {
    return {
      /**
       * Controls how the tooltip will be aligned to the current drag position.
       *
       * See {@link Core.helper.util.Rectangle#function-alignTo} for details.
       * @config {String}
       * @default
       */
      align: "t10-b50",
      /**
       * The number of pixels to offset from the drag position.
       * @config {Number}
       * @default
       */
      offset: 20,
      /**
       * The tooltip to be shown, hidden and repositioned to track the drag position.
       * @config {Core.widget.Tooltip}
       */
      tooltip: {
        $config: ["lazy", "nullify"],
        value: {
          type: "tooltip"
        }
      }
    };
  }
  open() {
    this.getConfig("tooltip");
  }
  close() {
    var _a5;
    (_a5 = this.tooltip) == null ? void 0 : _a5.hide();
  }
  dragMove(drag) {
    const { offset, tooltip } = this, { event } = drag;
    if (tooltip) {
      if (!tooltip.isVisible) {
        tooltip.show();
      }
      tooltip.alignTo({
        align: this.align,
        target: new Rectangle(event.clientX - offset, event.clientY - offset, offset * 2, offset * 2)
      });
    }
  }
  changeTooltip(config, existing) {
    return Widget.reconfigure(
      existing,
      config,
      /* owner = */
      this
    );
  }
};
__publicField(DragTipProxy, "type", "tip");
DragTipProxy.initClass();
DragTipProxy._$name = "DragTipProxy";

// ../Core/lib/Core/widget/mixin/Labelable.js
var Labelable_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    get hasLabel() {
      return Boolean(this.label);
    }
    compose() {
      const { hasLabel, labelPosition } = this;
      return {
        class: {
          [`b-label-${labelPosition}`]: hasLabel,
          "b-has-label": hasLabel
        }
      };
    }
    changeLabel(label) {
      return label || "";
    }
    setupLabel(lbl) {
      return ObjectHelper.assign({
        tag: "label",
        for: `${this.id}-input`,
        class: `b-label b-align-${lbl.align || "start"}`
      }, lbl);
    }
    updateLabelWidth(newValue) {
      if (this.labelElement) {
        this.labelElement.style.flex = `0 0 ${DomHelper.setLength(newValue)}`;
        if (this.inputWrap) {
          this.inputWrap.style.flexBasis = newValue == null ? "" : "auto";
        }
      }
    }
    //endregion
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, //region Config
  __publicField(_a5, "$name", "Labelable"), __publicField(_a5, "configurable", {
    /**
     * Get/set fields label. Please note that the Field needs to have a label specified from start for this to
     * work, otherwise no element is created.
     * @member {String} label
     * @category Misc
     */
    /**
     * Label, prepended to field
     * @config {String}
     * @category Label
     */
    label: null,
    /**
     * Label position, either 'before' the field or 'above' the field
     * @config {'before'|'above'}
     * @default
     * @category Label
     */
    labelPosition: "before",
    /**
     * CSS class name or class names to add to any configured {@link #config-label}
     * @config {String|Object}
     * @category Label
     */
    labelCls: null,
    /**
     * The width to apply to the `<label>` element. If a number is specified, `px` will be used.
     * @config {String|Number}
     * @localizable
     * @category Label
     */
    labelWidth: {
      value: null,
      $config: {
        localeKey: "L{labelWidth}"
      }
    }
  }), _a5;
};

// ../Core/lib/Core/widget/mixin/Validatable.js
var Validatable_default = (Target = Base) => {
  var _a5;
  return _a5 = class extends Target.mixin(Localizable_default) {
    get widgetClass() {
    }
    doDestroy() {
      const me = this, errorTip = me._rootElement && me.constructor.getSharedErrorTooltip(me._rootElement, {
        doNotCreate: true,
        cachePath: me.errorTooltipCachePath
      });
      super.doDestroy();
      if ((errorTip == null ? void 0 : errorTip.field) === me) {
        errorTip.hide();
      }
    }
    /**
     * A singleton error tooltip which activates on hover of invalid fields.
     * before show, it gets a reference to the field and interrogates its
     * active error list to display as the tip content.
     * @member {Core.widget.Tooltip}
     * @readonly
     * @category Misc
     */
    get errorTip() {
      return this.constructor.getSharedErrorTooltip(this.rootElement, {
        cachePath: this.errorTooltipCachePath,
        tooltipConfig: this.errorTooltipConfig
      });
    }
    /**
     * A singleton error tooltip which activates on hover of invalid fields.
     * before show, it gets a reference to the field and interrogates its
     * active error list to display as the tip content.
     *
     * Please note: Not applicable when using widgets inside a shadow root
     * @member {Core.widget.Tooltip}
     * @readonly
     * @static
     */
    static get errorTip() {
      return this.getSharedErrorTooltip(document.body);
    }
    static getSharedErrorTooltip(rootElement, config = {}) {
      var _a6;
      const {
        doNotCreate,
        cachePath = "errorTooltip",
        tooltipConfig
      } = config;
      let sharedErrorTooltip = (_a6 = rootElement.bryntum) == null ? void 0 : _a6[cachePath];
      if (!sharedErrorTooltip && !doNotCreate) {
        rootElement.bryntum = rootElement.bryntum || {};
        sharedErrorTooltip = Tooltip.new({
          cls: "b-field-error-tip",
          forSelector: ".b-field.b-invalid .b-field-inner",
          align: {
            align: "l-r",
            monitorTargetMutation: true
          },
          axisLock: "flexible",
          scrollAction: "realign",
          trapFocus: false,
          rootElement,
          onBeforeShow() {
            const tip = this, field = Widget.fromElement(tip.activeTarget);
            if (field) {
              const errors = field.getErrors();
              if (errors) {
                tip.html = errors.join("<br>");
                tip.field = field;
                return true;
              }
            }
            return false;
          }
        }, tooltipConfig);
        rootElement.bryntum[cachePath] = sharedErrorTooltip;
      }
      return sharedErrorTooltip;
    }
    /**
     * Adds an error message to the list of errors on this field.
     * By default, the field's valid/invalid state is updated; pass
     * `false` as the second parameter to disable that if multiple
     * changes are being made to the error state.
     *
     * Note, that you need to manually remove the added error with the {@link #function-clearError} method
     * to "release" the normal data update process (invalid data won't be synced). You can also use the 3rd
     * argument of this method to automatically remove the error upon the next user interaction.
     *
     * @param {String} error A locale string, or message to use as an error message.
     * @param {Boolean} [silent=false] Pass as `true` to skip updating the field's valid/invalid state.
     * @param {Boolean} [temporary=false] Pass as `true` to remove the error upon the next user interaction.
     */
    setError(error, silent, temporary) {
      const me = this, nbrErrorsBefore = Object.keys(me.errors || {}).length;
      (me.errors || (me.errors = {}))[error] = me.optionalL(error);
      const currentNbrErrors = Object.keys(me.errors).length;
      if (currentNbrErrors !== nbrErrorsBefore) {
        me.trigger("validityChange", { valid: currentNbrErrors === 0, bubbles: true });
      }
      if (!silent) {
        me.syncInvalid();
      }
      if (temporary) {
        const clearError = () => {
          me.clearError(error);
          remover();
        }, remover = me.ion({
          keydown: clearError,
          focusOut: clearError
        });
      }
    }
    /**
     * Removes an error message from the list of errors on this field.
     *
     * By default, the field's valid/invalid state is updated; pass `false` as the second parameter to disable that if
     * multiple changes are being made to the error state.
     *
     * @param {String} [error] A locale string, or message to remove. If not passed, all errors are cleared.
     * @param {Boolean} [silent=false] Pass as `true` to skip updating the field's valid/invalid state.
     */
    clearError(error, silent) {
      const me = this;
      if (me.errors && Object.keys(me.errors).length > 0) {
        if (error) {
          delete me.errors[error];
        } else {
          me.errors = {};
        }
        me.trigger("validityChange", { valid: Object.keys(me.errors).length === 0, bubbles: true });
      }
      if (!silent) {
        me.syncInvalid();
      }
    }
    /**
     * Returns an array of error messages as set by {@link #function-setError}, or
     * `undefined` if there are currently no errors.
     * @returns {String[]} The errors for this field, or `undefined` if there are no errors.
     */
    getErrors() {
      var _a6, _b2;
      const me = this;
      if (!me.isValid) {
        const validity = me.validity, stateName = ObjectHelper.keys(validity).find((key) => key !== "valid" && key !== "customError" && validity[key]), errorValues = me.errors && Object.values(me.errors), percent = (_b2 = (_a6 = me.formatter) == null ? void 0 : _a6.is.percent) != null ? _b2 : false;
        let errors;
        if (errorValues == null ? void 0 : errorValues.length) {
          errors = errorValues;
        } else if (validity.customError) {
          errors = [me.input.validationMessage];
        } else if (stateName) {
          errors = [me.L(stateName, {
            // In case min/max limits are present they will be used in the translation
            min: percent ? `${me.min * 100}%` : me.min,
            max: percent ? `${me.max * 100}%` : me.max
          })];
        } else {
          errors = [me.L(me.invalidValueError)];
        }
        if ((errors == null ? void 0 : errors.length) > 0) {
          return errors;
        }
      }
    }
    syncInvalid() {
      const me = this, { isPainted } = me;
      me.updatingInvalid = true;
      if (isPainted) {
        const { isValid, element, inputWrap } = me;
        element.classList[isValid ? "remove" : "add"]("b-invalid");
        if (isValid) {
          const errorTip = me.constructor.getSharedErrorTooltip(me.rootElement, {
            doNotCreate: true,
            cachePath: this.errorTooltipCachePath
          });
          if ((errorTip == null ? void 0 : errorTip.isVisible) && errorTip.field === me) {
            errorTip.hide();
          }
        } else if (inputWrap) {
          const errorTip = me.errorTip;
          if (errorTip && (me.containsFocus || me.isVisible && DomHelper.isDescendant(inputWrap, Tooltip.currentOverElement))) {
            if (errorTip.activeTarget === inputWrap && errorTip.isVisible) {
              errorTip.onBeforeShow();
            } else {
              errorTip.activeTarget = inputWrap;
              errorTip.showBy(inputWrap);
            }
          }
        }
      }
      me.updatingInvalid = false;
    }
  }, __publicField(_a5, "$name", "Validatable"), _a5;
};

// ../Core/lib/Core/widget/FieldContainer.js
var FieldContainer = class extends Container {
  static get configurable() {
    return {
      /**
       * An animation config object to use when expanding or collapsing the field's
       * {@link Core.widget.Field#config-container}.
       * @config {Object} animation
       * @property {Number} [animation.duration=300] The duration of the animation (in milliseconds).
       * @internal
       */
      animation: {
        duration: 300
      },
      /**
       * Controls whether the field is collapsed (that is, the field's {@link Core.widget.Field#config-container}
       * is hidden).
       * @config {Boolean}
       * @default false
       */
      collapsed: null,
      /**
       * The animator performing the field's currently running expand or collapse animation.
       * @config {Core.util.Animator}
       * @private
       */
      collapser: {
        value: null,
        $config: "nullify"
        // to abort animations on destroy
      },
      /**
       * A mapping object for config properties of the items in the {@link Core.widget.Field#config-container}.
       * The keys are the config names and the values are functions that compute the config value when passed
       * the field instance.
       *
       * For example, this is the default:
       * ```javascript
       *      syncableConfigs : {
       *          disabled : field => field.disabled
       *      }
       * ```
       * This indicates that the config property named with the key ('disabled') should be assigned to the result
       * of the function assigned to that key (`field => field.disabled`). In other words, when the field is
       * {@link Core.widget.Field#config-disabled}, all of the field's items should also be disabled.
       *
       * @config {Object}
       * @internal
       */
      syncableConfigs: null,
      /**
       * This object holds truthy values for each config property that, when modified, should trigger a sync of
       * this field's items as defined in {@link #config-syncableConfigs}.
       * @config {Object}
       * @internal
       */
      syncConfigTriggers: {
        $config: {
          merge: "classList"
        },
        value: null
      },
      testConfig: {
        animation: {
          duration: 10
        }
      }
    };
  }
  static get delayable() {
    return {
      syncChildConfigs: "raf"
    };
  }
  get inline() {
    var _a5;
    return (_a5 = this.owner.inline) != null ? _a5 : this.ensureItems().count === 1;
  }
  changeCollapsed(collapsed) {
    if (this.togglingCollapse) {
      this.togglingCollapse = false;
      return collapsed;
    }
    this.toggleCollapse(Boolean(collapsed));
  }
  updateCollapsed(collapsed) {
    var _a5;
    this.collapser = (_a5 = this.collapser) == null ? void 0 : _a5.destroy();
    this.setCollapsedCls(collapsed);
  }
  updateCollapser(collapser, was) {
    if (was && was.completed == null) {
      if (!was.reverting || !collapser) {
        was.destroy();
      }
    }
    this.setOwnerCls("b-collapsing", collapser);
  }
  //endregion
  /**
   * This property is `true` if the field container is currently collapsing.
   * @property {Boolean}
   * @readonly
   */
  get collapsing() {
    const { collapser } = this;
    return collapser != null && collapser.collapsed;
  }
  /**
   * This property is `true` if the field container is currently either collapsing or expanding.
   * @property {Boolean}
   * @readonly
   * @internal
   */
  get collapsingExpanding() {
    return this.collapser != null;
  }
  /**
   * This property is `true` if the field container is currently expanding.
   * @property {Boolean}
   * @readonly
   */
  get expanding() {
    const { collapser } = this;
    return collapser != null && !collapser.collapsed;
  }
  collapse(animation) {
    this.toggleCollapse(true, animation);
  }
  expand(animation) {
    this.toggleCollapse(false, animation);
  }
  setCollapsedCls(collapsed) {
    this.setOwnerCls("b-collapsed", collapsed);
  }
  setOwnerCls(cls, state) {
    var _a5, _b2;
    (_b2 = (_a5 = this.owner) == null ? void 0 : _a5.element) == null ? void 0 : _b2.classList[state ? "add" : "remove"](cls);
  }
  syncChildConfigs() {
    const me = this, { owner, syncableConfigs } = me;
    if (syncableConfigs) {
      let destProp, val;
      for (destProp in syncableConfigs) {
        val = syncableConfigs[destProp](owner);
        me.eachWidget(
          (item) => {
            item[destProp] = val;
          },
          /* deep = */
          false
        );
      }
    }
    owner.afterSyncChildConfigs(me);
  }
  syncContainer() {
    const { inline, layout } = this;
    if (this.autoLayout) {
      layout.horizontal = inline;
      layout.justify = inline ? "center" : "stretch";
    }
  }
  toggleCollapse(collapsed, animation) {
    const me = this, { animation: collapseAnimation, collapser, inline } = me, { containerWrapElement } = me.owner, finalize = (complete) => {
      if (complete) {
        me.element.style.height = "";
        me.togglingCollapse = true;
        me.collapsed = collapsed;
        me.collapser = null;
      }
    };
    if (collapsed == null) {
      collapsed = !me.collapsed;
    }
    if (animation !== false && animation !== null) {
      if (!containerWrapElement || !me.isVisible) {
        animation = null;
      } else {
        if (animation === true) {
          animation = {};
        } else if (typeof animation === "number") {
          animation = {
            duration: animation
          };
        }
        animation = collapseAnimation || animation ? ObjectHelper.merge({}, collapseAnimation, animation) : null;
      }
    }
    if (!animation) {
      me.togglingCollapse = true;
      me.collapsed = collapsed;
    } else if (collapser && collapsed !== collapser.collapsed) {
      me.collapser = collapser.revert({ finalize });
      me.collapser.collapsed = collapsed;
    } else if (!collapser && collapsed !== me.collapsed) {
      !collapsed && me.setCollapsedCls(false);
      const { element } = me, { height } = element.getBoundingClientRect(), expanded = inline ? 1 : height;
      !collapsed && me.setCollapsedCls(true);
      element.style.height = `${height}px`;
      me.collapser = Animator.run(ObjectHelper.merge({
        finalize,
        element: containerWrapElement,
        [inline ? "opacity" : "height"]: {
          from: collapsed ? expanded : 0,
          to: collapsed ? 0 : expanded
        }
      }, animation));
      me.collapser.collapsed = collapsed;
    }
  }
};
//region Config
__publicField(FieldContainer, "$name", "FieldContainer");
__publicField(FieldContainer, "type", "fieldcontainer");
FieldContainer.initClass();
FieldContainer._$name = "FieldContainer";

// ../Core/lib/Core/widget/Field.js
var byWeight = (l, r) => (l.weight || 0) - (r.weight || 0);
var byWeightReverse = (l, r) => (r.weight || 0) - (l.weight || 0);
var emptyArray8 = [];
var triggerConfigs = {
  align: true,
  weight: true
};
var _Field = class _Field extends Widget.mixin(Badge_default, Labelable_default, Validatable_default) {
  static get delayable() {
    return {
      highlightChanged: "raf"
    };
  }
  doDestroy() {
    var _a5, _b2, _c2;
    const me = this, { triggers } = me;
    (_a5 = me.inputListenerRemover) == null ? void 0 : _a5.call(me);
    (_b2 = me.keyListenerRemover) == null ? void 0 : _b2.call(me);
    (_c2 = me.container) == null ? void 0 : _c2.destroy();
    super.doDestroy();
    if (triggers) {
      for (const t of Object.values(triggers)) {
        t.destroy();
      }
    }
  }
  get childItems() {
    const { container, triggers } = this, ret = triggers ? Object.values(triggers) : [];
    return container ? [...ret, ...container.childItems] : ret;
  }
  get invalidValueError() {
    return "L{invalidValue}";
  }
  //endregion
  //region Event
  /**
   * Fired when the user types into this field.
   * @event input
   * @param {Core.widget.Field} source This field
   * @typings source -> {Core.widget.Field|any}
   * @param {String} value - This field's value
   * @typings value -> {String|Number|Boolean|any}
   * @param {Event} event - The triggering DOM event
   */
  /**
   * Fired when this field's value changes.
   * @event change
   * @param {Core.widget.Field} source - This Field
   * @typings source -> {Core.widget.Field|any}
   * @param {String} value - This field's value
   * @typings value -> {String|Number|Boolean|any}
   * @param {String} oldValue - This field's previous value
   * @typings oldValue -> {String|Number|Boolean|any}
   * @param {Boolean} valid - True if this field is in a valid state
   * @param {Event} event - The triggering DOM event if any
   * @param {Boolean} userAction - Triggered by user taking an action (`true`) or by setting a value (`false`)
   *
   * @typings checked -> {Boolean} // Used for compatibility with Checkbox event
   */
  /**
   * User performed default action (typed into this field).
   * @event action
   * @param {Core.widget.Field} source - This Field
   * @typings source -> {Core.widget.Field|any}
   * @param {String} value - This field's value
   * @typings value -> {String|Number|Boolean|any}
   * @param {String} oldValue - This field's previous value
   * @typings oldValue -> {String|Number|Boolean|any}
   * @param {Boolean} valid - True if this field is in a valid state
   * @param {Event} event - The triggering DOM event if any
   * @param {Core.data.Model} record Selected record. Available for fields with records selection functionality
   * @param {Core.data.Model[]} records Selected records as an array. Available for fields with records selection functionality
   * @param {Boolean} userAction - Triggered by user taking an action (`true`) or by setting a value (`false`)
   *
   * @typings checked -> {Boolean} // Used for compatibility with Checkbox event
   */
  /**
   * Fired when this field is {@link #function-clear cleared}.
   *
   * This will be triggered when a user clicks this field's clear {@link #property-triggers trigger}
   * @event clear
   * @param {Core.widget.Field} source - This Field
   * @typings source -> {Core.widget.Field|any}
   */
  /**
   * User clicked one of this field's {@link #property-triggers}
   * @event trigger
   * @param {Core.widget.Field} source This field
   * @typings source -> {Core.widget.Field|any}
   * @param {Core.widget.Widget} trigger The trigger activated by click or touch tap.
   */
  //endregion
  //region Init
  construct(config = {}) {
    super.construct(config);
    const me = this;
    if (me.keyStrokeChangeDelay) {
      me.changeOnKeyStroke = me.buffer(me.internalOnChange, me.keyStrokeChangeDelay);
    }
    if (!me.ariaLabel && me.placeholder) {
      me.updateAriaLabel(me.placeholder);
    }
  }
  // Adding triggers keymap to field keymap with prefix 'triggerName.';
  changeKeyMap(keyMap) {
    var _a5;
    const me = this;
    let mergedKeyMap = {};
    if (keyMap) {
      if (!me._fieldKeyMap) {
        me._fieldKeyMap = keyMap;
        ObjectHelper.assign(mergedKeyMap, keyMap);
      } else {
        ObjectHelper.assign(mergedKeyMap, me._fieldKeyMap);
      }
    }
    for (const triggerName in me.triggers) {
      if ((_a5 = me.triggers[triggerName]) == null ? void 0 : _a5.keyMap) {
        mergedKeyMap = me.mergeKeyMaps(mergedKeyMap, me.triggers[triggerName].keyMap, triggerName);
      }
    }
    return mergedKeyMap;
  }
  get keyMapSubComponents() {
    return this.triggers;
  }
  onFocusIn(e) {
    const me = this;
    me.valueOnFocus = ObjectHelper.clone(me.value);
    me.validOnFocus = !(me.errors && Object.keys(me.errors).length);
    super.onFocusIn(e);
    me.focusing = true;
    me.syncInvalid();
    me.focusing = false;
  }
  onFocusOut(e) {
    super.onFocusOut(e);
    this.syncRequired();
    this.onEditComplete();
  }
  /**
   * Template function which may be implemented by subclasses to synchronize
   * input state and validity state upon completion of the edit.
   * @internal
   */
  onEditComplete() {
  }
  get rootUiClass() {
    return _Field;
  }
  compose() {
    this.getConfig("clearable");
    const me = this, { container, innerElements, label, labels, labelCls, readOnly, stepTriggers, triggers, required, showRequiredIndicator } = me, triggerInstances = ObjectHelper.values(triggers, (k, v) => !v), startTriggers = triggerInstances.filter((t) => t.align === "start").sort(byWeight), endTriggers = triggerInstances.filter((t) => t.align !== "start").sort(byWeightReverse), setupLabel = me.setupLabel.bind(me), innerChildren = [
      ...startTriggers.map((t) => t.element),
      ...innerElements,
      ...endTriggers.map((t) => t.element)
    ];
    return {
      class: {
        "b-has-step-triggers": stepTriggers,
        "b-has-start-trigger": startTriggers.length,
        "b-no-input": !innerElements.length,
        "b-readonly": readOnly,
        "b-field-container": Boolean(container),
        "b-field-container-inline": container == null ? void 0 : container.inline,
        "b-required": Boolean(required && showRequiredIndicator)
      },
      children: [
        ...(labels || emptyArray8).filter((t) => t && t.align !== "end").map(setupLabel),
        (label || null) && setupLabel({
          reference: "labelElement",
          class: `b-label b-align-start ${labelCls || ""}`,
          html: label
        }),
        innerChildren.length ? {
          class: "b-field-inner",
          reference: "inputWrap",
          children: innerChildren
        } : null,
        ...(labels || emptyArray8).filter((t) => (t == null ? void 0 : t.align) === "end").map(setupLabel),
        ...container ? [{
          reference: "containerWrapElement",
          children: [container.element],
          class: {
            "b-field-container-wrap": 1
          }
        }] : emptyArray8
      ]
    };
  }
  updateElement(element, was) {
    var _a5;
    const me = this, value2 = me.initialConfig.value, { input } = me;
    super.updateElement(element, was);
    if (value2 != null) {
      me.value = value2;
    }
    me.syncEmpty();
    me.updateInputReadOnly(me.inputReadOnly);
    me.syncInvalid();
    const keyEventElement = input || me.focusElement;
    (_a5 = me.keyListenerRemover) == null ? void 0 : _a5.call(me);
    me.keyListenerRemover = keyEventElement && EventHelper.on({
      element: keyEventElement,
      thisObj: me,
      keydown: "internalOnKeyEvent",
      keypress: "internalOnKeyEvent",
      keyup: "internalOnKeyEvent"
    });
  }
  // Subclasses may implement this.
  // Needed because Checkbox produces an array of two elements as its innerElements
  get innerElements() {
    return [this.inputElement];
  }
  /**
   * The input element at the heart if this field
   * @member {HTMLElement} input
   */
  get inputElement() {
    const {
      attributes,
      inputAttributes,
      id: id2,
      inputCls,
      inputType,
      name,
      inputTag
    } = this, domConfig = ObjectHelper.merge({
      reference: "input",
      tag: inputTag,
      type: inputType,
      name: name || id2,
      id: `${id2}-input`,
      retainElement: "prevent-release",
      // allow the input to be transplanted as in combo/chipView
      // Disable OnePassword on fields unless it is a password field
      dataset: this.isPasswordField ? null : {
        opIgnore: true
      }
    }, inputAttributes);
    if (inputCls) {
      domConfig.class = inputCls;
    }
    for (let key, value2, i = attributes.length; i-- > 0; ) {
      key = attributes[i];
      value2 = this[key];
      if (value2 != null) {
        domConfig[StringHelper.hyphenateAttributeName(key)] = value2;
      }
    }
    return domConfig;
  }
  //endregion
  //region Focus & select
  get caretPos() {
    return this.textSelection[0];
  }
  set caretPos(value2) {
    this.textSelection = value2;
  }
  get focusElement() {
    return this.input;
  }
  get textSelection() {
    var _a5;
    const input = this.input;
    let ret;
    try {
      ret = [input.selectionStart, input.selectionEnd, input.selectionDirection];
    } catch (e) {
      ret = ((_a5 = input.value) == null ? void 0 : _a5.length) || 0;
      ret = [ret, ret];
    }
    return ret;
  }
  set textSelection(value2) {
    if (typeof value2 === "number") {
      this.select(value2, value2);
    } else {
      this.select(...value2);
    }
  }
  get hasTextSelection() {
    const [selectionStart, selectionEnd] = this.textSelection;
    return selectionStart && selectionEnd - selectionStart > 0;
  }
  /**
   * Returns the input value for this field's input element that will be present if
   * the event carrying the given text is allowed to proceed.
   * @param {String} text
   * @returns {String}
   * @private
   */
  getAfterValue(text) {
    const [begin, end] = this.textSelection, value2 = this.input.value;
    return `${value2.substr(0, begin)}${text}${value2.substr(end || begin)}`;
  }
  /**
   * Selects the field contents. Optionally may be passed a start and end.
   * @param {Number} [start] The start index from which to select the input.
   * @param {Number} [end] The index at which to end the selection of the input.
   */
  select(start, end) {
    const input = this.focusElement;
    if (input.value.length) {
      if (arguments.length === 0) {
        this.selectAll();
        return;
      }
      if (!this.supportsTextSelection) {
        return;
      }
      input.setSelectionRange(start, end);
    }
  }
  moveCaretToEnd() {
    const input = this.input;
    if (input.createTextRange) {
      const range = input.createTextRange();
      range.collapse(false);
      range.select();
    } else if (this.supportsTextSelection) {
      this.select(input.value.length, input.value.length);
    }
  }
  selectAll() {
    this.focusElement.select();
  }
  get caretAtEnd() {
    return this.textSelection[1] === this.input.value.length;
  }
  get caretAtStart() {
    return !this.textSelection[1];
  }
  get selectedAll() {
    const { textSelection } = this;
    return !textSelection[0] && textSelection[1] === this.input.value.length;
  }
  // called on value changes to update styling of empty vs non-empty field
  syncEmpty() {
    var _a5;
    const { isEmptyInput, isEmpty, element } = this, clear = (_a5 = this.triggers) == null ? void 0 : _a5.clear, empty2 = isEmptyInput && isEmpty;
    if (element) {
      if (clear) {
        clear[empty2 ? "hide" : "show"]();
      }
      element.classList.toggle("b-empty", empty2);
    }
  }
  updateHint() {
    this.syncHint();
  }
  updateHintHtml() {
    this.syncHint();
  }
  syncHint() {
    const me = this, { input, hint, hintHtml } = me, parent = input == null ? void 0 : input.parentElement;
    if (input) {
      let hintValue = hintHtml || hint;
      const hintElement = me.hintElement || hintValue && (me.hintElement = DomHelper.createElement({
        parent,
        className: "b-field-hint",
        nextSibling: input.nextSibling,
        children: [{
          className: "b-field-hint-content"
        }]
      }));
      if (hintElement) {
        if (typeof hintValue === "function") {
          hintValue = hintValue({ source: me, value: me.value });
        }
        hintElement.firstChild[hintHtml ? "innerHTML" : "textContent"] = hintValue || "";
      }
      me.element.classList[hintValue ? "remove" : "add"]("b-field-no-hint");
    }
  }
  //endregion
  //region Getters/setters
  updateInputTag(tag) {
    if (tag && tag !== this.getDefaultConfiguration().inputTag) {
      this.dontCache = true;
    }
  }
  updateEditable() {
    this.syncInputReadOnly();
  }
  syncInputReadOnly() {
    this.getConfig("readOnly");
    this.inputReadOnly = this._readOnly || this.editable === false;
  }
  updateInputReadOnly(readOnly) {
    const me = this, { input, inputListenerRemover } = me;
    if (input) {
      input.readOnly = readOnly ? "readOnly" : null;
      if (readOnly) {
        me.inputListenerRemover = inputListenerRemover == null ? void 0 : inputListenerRemover();
      } else if (!inputListenerRemover) {
        me.inputListenerRemover = EventHelper.on({
          element: input,
          thisObj: me,
          focus: "internalOnInputFocus",
          change: "internalOnChange",
          input: "internalOnInput"
        });
      }
    }
  }
  changeReadOnly(value2) {
    return Boolean(value2);
  }
  updateReadOnly(readOnly) {
    this.syncInputReadOnly();
  }
  updateRequired(required) {
    this.syncRequired();
  }
  updateClearable(clearable) {
    const me = this;
    me.getConfig("triggers");
    me.triggers = {
      clear: clearable && ObjectHelper.assign({
        cls: "b-icon-remove",
        weight: 1e3,
        handler: me.clear
      }, clearable) || null
    };
  }
  changeTriggers(triggers, was) {
    const me = this, manager = me.$triggers || (me.$triggers = new DynamicObject({
      configName: "triggers",
      factory: _Field.Trigger,
      inferType: false,
      // the name of a trigger in the triggers object is not its type
      owner: me,
      created(instance) {
        FunctionHelper.after(instance, "onConfigChange", (ret, { name }) => {
          if (triggerConfigs[name]) {
            me.onConfigChange({
              name: "triggers",
              value: manager.target
            });
          }
        });
      },
      setup(config, name) {
        config.reference = config.ref = name;
        config.parent = me;
      }
    }));
    if (triggers && !me.stepTriggers && (triggers.back || triggers.forward)) {
      triggers = ObjectHelper.assign({}, triggers);
      delete triggers.back;
      delete triggers.forward;
    }
    manager.update(triggers);
    if (me.element) {
      me.keyMap = me.keyMap;
    }
    if (!was) {
      return manager.target;
    }
  }
  updateInputWidth(newValue) {
    this.input.style.width = DomHelper.setLength(newValue);
    this.element.classList.add("b-has-width");
  }
  updateInputAlign(newValue) {
    this.input.style.textAlign = newValue;
  }
  /**
   * Returns true if the field value is valid
   * @type {Boolean}
   * @readonly
   */
  get isValid() {
    const me = this;
    if (!me.disabled && !me.skipValidation) {
      me.syncRequired();
      if (me.checkValidity) {
        me.callback(me.checkValidity, me, [me]);
      }
      if (me.errors && Object.keys(me.errors).length) {
        return false;
      }
      const validity = me.validity;
      if (validity) {
        return validity.valid;
      }
    }
    return true;
  }
  get isClear() {
    return this.isEmpty && this.isEmptyInput;
  }
  /**
   * Returns `true` if this field is empty. That is, if it would violate the {@link #config-required}
   * setting.
   *
   * This may have different definitions in subclasses from simple text fields.
   * @type {Boolean}
   * @readonly
   */
  get isEmpty() {
    const { value: value2 } = this;
    return value2 == null || value2 === "" || value2 === this.nullValue;
  }
  /**
   * Returns true if the field's input is empty
   * @type {Boolean}
   * @readonly
   */
  get isEmptyInput() {
    return !this.input || this.input.value == null || this.input.value === "";
  }
  /**
   * Returns the DOM `ValidityState` for this widget's input element, or `null` if there
   * isn't one.
   * @returns {ValidityState}
   * @private
   */
  get validity() {
    const input = this.input;
    return input == null ? void 0 : input.validity;
  }
  // Called by Editor.js, for overriding in subclasses that require async setting of values (custom editors in React)
  async setValue(value2) {
    this.value = value2;
  }
  changeValue(value2, was) {
    if (value2 == null) {
      value2 = this.nullValue;
    }
    if (this.hasChanged(was, value2)) {
      return value2;
    }
    if (value2 === "") {
      this.syncRequired();
    }
    return was;
  }
  updateValue(value2, oldValue) {
    const me = this;
    if (!me.isConfiguring) {
      me.syncRequired();
      if (!me.inputting) {
        me.triggerChange();
      }
    }
    if (!me.inputting) {
      me._lastValue = value2;
    }
    me.syncInputFieldValue();
  }
  /**
   * Compares this field's value with its previous value. May be overridden in subclasses
   * which have more complex value types. See, for example, {@link Core.widget.DurationField}.
   * @param {*} oldValue
   * @param {*} newValue
   * @private
   */
  hasChanged(oldValue, newValue) {
    return newValue !== oldValue;
  }
  /**
   * Called by the base Field class's `set value` to sync the state of the UI with the field's value.
   *
   * Relies upon the class implementation of `get inputValue` to return a string representation of
   * the value for user consumption and editing.
   *
   * This method can be overriden.
   *
   * @param {Boolean} [skipHighlight] skip highlighting of the field
   * @returns {void}
   * @advanced
   */
  syncInputFieldValue(skipHighlight = false) {
    const me = this, { input, parent, inputValueAttr, inputValue } = me;
    if (input && !me.inputting && input[inputValueAttr] !== inputValue) {
      input[inputValueAttr] = inputValue;
      if (!me.isConfiguring && !me.containsFocus && me.highlightExternalChange) {
        input.classList.remove("b-field-updated");
        me.clearTimeout("removeUpdatedCls");
        if (parent) {
          const { assigningValues } = parent;
          if (assigningValues === true || (assigningValues == null ? void 0 : assigningValues.highlight) === false) {
            skipHighlight = true;
          }
        }
        if (!skipHighlight) {
          me.highlightChanged();
        }
      }
    }
    me.syncEmpty();
    me.syncInvalid();
  }
  highlightChanged() {
    this.input.classList.add("b-field-updated");
    this.setTimeout("removeUpdatedCls", this.updatedClsDuration);
  }
  removeUpdatedCls() {
    this.input.classList.remove("b-field-updated");
  }
  /**
   * A String representation of the value of this field for {@link #function-syncInputFieldValue} to use
   * as the input element's value.
   *
   * Subclasses may override this to create string representations.
   *
   * For example, {@link Core.widget.DateField}'s implementation will format the field date
   * value according to its configured {@link Core.widget.DateField#config-format}. And {@link Core.widget.Combo}'s
   * implementation will return the {@link Core.widget.Combo#config-displayField} of the selected record.
   * @internal
   * @readOnly
   */
  get inputValue() {
    return this.value == null ? "" : this.value;
  }
  get inputValueAttr() {
    return "value";
  }
  get supportsTextSelection() {
    const input = this.focusElement;
    return input && DomHelper.isTextualInput(input);
  }
  updatePlaceholder(placeholder) {
    if (!this.ariaLabel) {
      this.updateAriaLabel(placeholder);
    }
    if (this.input) {
      if (placeholder == null) {
        this.input.removeAttribute("placeholder");
      } else {
        this.input.placeholder = placeholder;
      }
    }
  }
  //endregion
  //region Events
  internalOnInputFocus() {
    if (this.autoSelect) {
      this.selectAll();
    }
  }
  /**
   * Trigger event when fields input changes
   * @fires change
   * @private
   */
  internalOnChange(event) {
    const me = this;
    if (me.hasChanged(me._lastValue, me.value) && me.isValid) {
      me.triggerChange(event, true);
      me._lastValue = me.value;
    }
  }
  triggerChange(event, userAction = Boolean(this._isUserAction)) {
    const me = this, {
      value: value2,
      _lastValue: oldValue,
      isValid: valid
    } = me;
    me.syncHint();
    me.triggerFieldChange({ value: value2, oldValue, event, userAction, valid });
    if (me.defaultAction === "change") {
      me.trigger("action", { value: value2, oldValue, event, userAction, valid });
    }
  }
  fetchInputValue() {
    this.value = this.input[this.inputValueAttr];
  }
  /**
   * Trigger event when user inputs into field
   * @fires input
   * @param event
   * @private
   */
  internalOnInput(event) {
    var _a5;
    const me = this;
    if (!me.validateOnInput) {
      me.errorTip.hide();
    }
    me.inputting = true;
    me.fetchInputValue();
    me.inputting = false;
    me.trigger("input", { value: me.value, event });
    (_a5 = me.changeOnKeyStroke) == null ? void 0 : _a5.call(me, event);
  }
  /**
   * Returns `true` if the {@link #property-input} field needs to be synced with the internal
   * {@link #property-value} of this field.
   *
   * May be overridden in subclasses where this is more complex such as multiSelect Combo
   * with a `ChipView` where the input area does not reflect the field's value.
   * @private
   */
  get needsInputSync() {
    var _a5;
    return this.input[this.inputValueAttr] !== String((_a5 = this.inputValue) != null ? _a5 : "");
  }
  internalOnKeyEvent(event) {
    const me = this, { value: value2 } = me;
    let stopEvent2 = false;
    me._isUserAction = true;
    if (event.type === "keydown") {
      if (event.key === "Escape" && !(me.readOnly || me.disabled)) {
        const wasValid = me.isValid, initialValue = "initialValue" in me ? me.initialValue : me.valueOnFocus, valueChanged = me.hasChanged(initialValue, value2), { needsInputSync } = me;
        if (me.revertOnEscape && (!wasValid || valueChanged || needsInputSync)) {
          if (valueChanged || !wasValid) {
            me.value = initialValue;
          }
          if (needsInputSync) {
            me.syncInputFieldValue(true);
          }
          me.clearError();
          stopEvent2 = (valueChanged || needsInputSync) && me.isValid || wasValid !== me.validOnFocus;
        } else if (!me.isEmpty && me.clearable && me.revertOnEscape && !wasValid) {
          me.clear();
          event.stopPropagation();
        }
      }
    }
    if (!me.isDestroyed) {
      if (stopEvent2) {
        event.stopImmediatePropagation();
      }
      me.trigger(event.type, { event });
    }
    me._isUserAction = false;
  }
  /**
   * Clears the value of this Field, and triggers the {@link #event-clear} event.
   */
  clear(event) {
    const me = this;
    if (!me.isClear) {
      me.value = me.nullValue;
      me.clearError();
      me.syncInvalid();
      if (me.input && DomHelper.isTextualInput(me.input)) {
        me.input.value = "";
      }
      me.trigger("clear", { event });
      event == null ? void 0 : event.stopImmediatePropagation();
    }
  }
  /**
   * Called when disabled state is changed.
   * Used to add or remove 'b-invalid' class for the invalid field based on current disabled state.
   * @private
   */
  onDisabled() {
    this.syncInvalid();
  }
  //endregion
  //region Error
  syncRequired() {
    var _a5;
    const me = this;
    if (!me.isConfiguring && (!me.focusing || me.focusVisible) && me.required && me.isEmpty && !((_a5 = me.parent) == null ? void 0 : _a5.isSettingValues)) {
      me.setError("L{fieldRequired}", me.updatingInvalid);
    } else {
      me.clearError("L{fieldRequired}", me.updatingInvalid);
    }
  }
  //endregion
  //region Container
  get containerCollapsed() {
    var _a5;
    return (_a5 = this.container) == null ? void 0 : _a5.collapsed;
  }
  set containerCollapsed(collapsed) {
    const { container } = this;
    if (container) {
      container.collapsed = collapsed;
    }
  }
  afterSyncChildConfigs(container) {
  }
  changeContainer(config, existing) {
    if (config && (Array.isArray(config) || !("type" in config || "items" in config))) {
      config = {
        items: config
      };
    }
    const me = this, defaults = config && ObjectHelper.merge({
      owner: me,
      layout: "box"
    }, me.containerDefaults), container = Widget.reconfigure(existing, config, {
      owner: me,
      defaults
    });
    if (container) {
      container.autoLayout = (defaults == null ? void 0 : defaults.layout) === "box";
      if (container === existing) {
        container.syncContainer();
        return;
      }
    }
    return container;
  }
  onConfigChange(info) {
    var _a5, _b2;
    super.onConfigChange(info);
    if ((_b2 = (_a5 = this.container) == null ? void 0 : _a5.syncConfigTriggers) == null ? void 0 : _b2[info.name]) {
      this.container.syncChildConfigs();
    }
  }
  assignValue(values, options) {
    const me = this, { container, containValues } = me, valueName = me.getValueName(options), storedValue = values && valueName in values;
    me.assigningValue = true;
    if (container && containValues !== false) {
      if (values && containValues === "nested") {
        if (storedValue) {
          values = ObjectHelper.clone(values[valueName]);
          super.assignValue({
            // Removing 'value:true' from nested values is a trick done by checkbox (could be done by
            // any toggle-like thing) so just put true back in:
            [valueName]: values && !("value" in values) || values.value
          }, options);
          if (values) {
            delete values.value;
          }
          container.assignValue(values, options);
        }
      } else {
        super.assignValue(values, options);
        container.assignValue(values, options);
      }
    } else {
      super.assignValue(values, options);
    }
    me.assigningValue = false;
  }
  gatherValue(values) {
    super.gatherValue(values);
    this.gatherValues(values);
  }
  gatherValues(values, storedValue) {
    const me = this, { container, containValues, valueName } = me, cvt = typeof containValues;
    storedValue = storedValue != null ? storedValue : valueName in values;
    if (container && containValues !== false) {
      if (containValues === "nested") {
        if (storedValue) {
          values[valueName] = {
            value: values[valueName],
            ...container.values
          };
        }
      } else if (containValues === true || containValues == null && storedValue || cvt === "string" && me[containValues] || cvt === "function" && containValues(me)) {
        container.gatherValues(values);
      }
    }
  }
  updateContainer(container, was) {
    was && this.detachListeners(was.id);
    if (container) {
      if (!container.rendered) {
        container.render();
      }
      container.syncContainer();
      container.syncChildConfigs();
    }
  }
  //endregion
};
//region Config
__publicField(_Field, "$name", "Field");
__publicField(_Field, "type", "field");
__publicField(_Field, "configurable", {
  // Override from Widget. Fields, having a semantically meaningful input element dopn't neeed a role.
  // They only need a role if they are a special input type such as combobox
  role: null,
  defaultBindProperty: "value",
  /**
   * @hideconfigs htmlCls, content, tag, scrollable, html
   */
  /**
   * Set to `true`, completely bypasses validation logic (could be userful if your field is not `editable`
   * to the user).
   *
   * @config {Boolean}
   * @default false
   */
  skipValidation: null,
  /**
   * Text to display in empty field.
   * @prp {String} placeholder
   */
  placeholder: null,
  /**
   * Gets or sets the value. The returned type will depend upon the Field subclass.
   *
   * `TextField` returns a `String`.
   *
   * `NumberField` returns a `Number`.
   *
   * `DateField` and `TimeField` return a `Date` object, and `null` if the field is empty.
   *
   * `Combo` will return a `String` if configured with `items` as a simple string array.
   * Otherwise it will return the {@link Core.widget.Combo#config-valueField} value from the
   * selected record, or `null` if no selection has been made.
   * @member {*} value
   */
  /**
   * Default value
   * @config {String}
   */
  value: "",
  /**
   * Name of the field which is used as a key to get/set values from/to the field.
   * Used prior to {@link Core.widget.Widget#config-ref ref} and {@link Core.widget.Widget#config-id id} in
   * {@link Core.widget.Container#property-values Container.values}.
   *
   * The config is useful when the field is used in EventEditor or TaskEditor to load/save values automatically.
   * @config {String}
   */
  name: null,
  /**
   * The labels to add either before or after the input field.
   * Each label may have the following properties:
   * * `html` The label text.
   * * `align` `'start'` or `'end'` which end of the field the label should go.
   * @config {Object[]}
   * @property {String} html Label text
   * @property {'start'|'end'} align Which end of the file the label should go
   * @category Label
   */
  labels: null,
  /**
   * Configure as `true` to indicate that a `null` field value is to be marked as invalid. This will
   * optionally append a * to the field label if {@link #property-showRequiredIndicator} is set.
   * @prp {Boolean}
   * @default false
   */
  required: null,
  /**
   *`true` to automatically display a * after the label for this field when it is {@link #property-required}.
   * @prp {String}
   */
  showRequiredIndicator: null,
  /**
   * Show a trigger to clear field, if this field is not {@link #config-readOnly}. The trigger is available
   * in the {@link #property-triggers} object under the name `clear`. May also be an object which
   * configures the `clear` {@link #property-triggers trigger}.
   * @config {Boolean|FieldTriggerConfig}
   * @default false
   */
  clearable: null,
  /**
   * If this field is not {@link #config-readOnly}, then setting this option means that pressing
   * the `ESCAPE` key after editing the field will revert the field to the value it had when
   * the user focused the field. If the field is _not_ changed from when focused, the {@link #config-clearable}
   * behaviour will be activated.
   * @config {Boolean}
   * @default false
   */
  revertOnEscape: null,
  /**
   * An optional string to display inside the input field as an overlay. This can be useful for displaying
   * a field's units.
   *
   * This config is ignored if {@link #config-hintHtml} is set.
   *
   * For example:
   * ```javascript
   *  {
   *      type  : 'numberfield',
   *      label : 'Temperature',
   *      hint  : '°C'
   *  }
   * ```
   *
   * This config can be set to a function to dynamically generate the `hint` text:
   * ```javascript
   *  {
   *      type  : 'numberfield',
   *      label : 'Duration',
   *      hint  : ({ value }) => (value === 1) ? 'Day' : 'Days'
   *  }
   * ```
   *
   * The function is passed an object with the following properties:
   *
   *  - `source` A reference to the field instance.
   *  - `value` The current value of the field.
   *
   * A `hint` function will be called when the field changes value.
   *
   * @config {String|Function}
   * @param {Object} data A data object
   * @param {Core.widget.Field} data.source A reference to the field instance
   * @param {*} data.value The current value of the field
   * @returns {String}
   * @category Label
   */
  hint: null,
  /**
   * This config is similar to {@link #config-hint} except that this config is used to display HTML content.
   * Since this can allow malicious content to be executed, be sure not to include user-entered data or to
   * encode such data (see {@link Core.helper.StringHelper#function-encodeHtml-static}).
   *
   * If this config is set, {@link #config-hint} is ignored.
   *
   * For example:
   * ```javascript
   *  {
   *      type     : 'numberfield',
   *      label    : 'Temperature',
   *      hintHtml : '<i>°C</i>'
   *  }
   * ```
   *
   * This config can be set to a function to dynamically generate the `hintHtml` text:
   * ```javascript
   *  {
   *      type     : 'numberfield',
   *      label    : 'Duration',
   *      hintHtml : ({ value }) => (value === 1) ? '<i>Day</i>' : '<i>Days</i>'
   *  }
   * ```
   *
   * The function is passed an object with the following properties:
   *
   *  - `source` A reference to the field instance.
   *  - `value` The current value of the field.
   *
   * A `hintHtml` function will be called when the field changes value.
   *
   * @config {String|Function}
   * @param {Object} data A data object
   * @param {Core.widget.Field} data.source A reference to the field instance
   * @param {*} data.value The current value of the field
   * @returns {String}
   * @category Label
   */
  hintHtml: null,
  /**
   * The width to apply to the `<input>` element. If a number is specified, `px` will be used.
   * @config {String|Number}
   * @category Input element
   */
  inputWidth: null,
  /**
   * The delay in milliseconds to wait after the last keystroke before triggering a change event.
   * Set to 0 to not trigger change events from keystrokes (listen for input event instead to have
   * immediate feedback, change will still be triggered on blur).
   *
   * If the field is {@link #config-clearable}, the change event fires immediately on receiving
   * the clear gesture.
   * @config {Number}
   * @default
   */
  keyStrokeChangeDelay: 0,
  /**
   * Makes the field unmodifiable by user action. The input area is not editable, and triggers
   * are unresponsive.
   *
   * This is a wider-acting setting than {@link #config-editable} which *only* sets the
   * `readOnly` attribute of the `<input>` field.
   *
   * {@link Core.widget.PickerField}s such as `Combo` and `DateField` can be `editable : false`, but still
   * modifiable through the UI.
   * @prp {Boolean}
   */
  readOnly: null,
  /**
   * Set to `false` to prevent user from editing the field. For TextFields it is basically the same as setting
   * {@link #config-readOnly}, but for PickerFields there is a distinction where it allows you to pick a value
   * but not to type one in the field.
   *
   * {@link Core.widget.PickerField}s such as `Combo` and `DateField` can be `editable : false`, but still
   * modifiable through the UI.
   *
   * On mobile devices, {@link Core.widget.PickerField}s are set to `editable : false` by
   * default so that the user must select a value from the dropdown picker rather than having
   * to type a value which will cause a display of the virtual keyboard.
   *
   * If typing is essential to the functioning of the field, configuring the field
   * with `editable : true` will override this behaviour.
   * @prp {Boolean}
   * @default true
   */
  editable: true,
  defaultAction: "change",
  stepTriggers: null,
  /**
   * The triggers to add either before or after the input field. Each property name is the reference by which
   * an instantiated Trigger Widget may be retrieved from the live `triggers` property.
   *
   * Each trigger may have the following properties:
   * * `cls` The CSS class to apply.
   * * `handler` A method in the field to call upon click
   * * `align` (Optional) `'start'` or `'end'` which end of the field the trigger should go.
   * * `weight` (Optional) Higher weighted triggers gravitate towards the input field.
   * * `key` (Optional) The [`key` name](https://developer.mozilla.org/en-US/docs/Web/API/UI_Events/Keyboard_event_key_values)
   * of the key to use as the activation key of this trigger.
   *
   * ```javascript
   * const textField = new TextField({
   *   triggers : {
   *       check : {
   *           cls : 'b-fa b-fa-check',
   *           handler() {
   *               ...
   *           }
   *       },
   *       ...
   *   }
   * })
   * ```
   *
   * @prp {Object<String,Core.widget.Widget>}
   * @accepts {Object<String,FieldTriggerConfig>}
   */
  triggers: null,
  /**
   * Specify `true` to highlight field after external value changes
   * @config {Boolean}
   */
  highlightExternalChange: false,
  localizableProperties: ["label", "title", "placeholder", "labelWidth", "hint"],
  /**
   * Specify `true` to auto select field contents on focus
   * @config {Boolean}
   * @default
   */
  autoSelect: false,
  /**
   * Sets the native `autocomplete` property of the underlying input element. For more information, please refer to
   * [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete)
   * @config {String}
   * @default
   * @category Input element
   */
  autoComplete: "off",
  /**
   * Sets the native `spellcheck` property of the underlying input element. For more information, please refer to
   * [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/spellcheck)
   * @config {Boolean}
   * @category Input element
   */
  spellCheck: null,
  /**
   * Set to `false` to not highlight a field as invalid while typing, to instead show it on ENTER key press
   * or similar.
   * @config {Boolean}
   * @category Input element
   */
  validateOnInput: true,
  /**
   * Sets custom attributes of the underlying input element. For more information, please refer to
   * [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes)
   * @config {Object<String,String>}
   * @default
   * @category Input element
   */
  inputAttributes: null,
  /**
   * Sets the `type` attribute of the underlying input element (password, hidden, date, color, etc.).
   * @config {String}
   * @category Input element
   */
  inputType: "text",
  /**
   * Text alignment for the input field.
   * @config {String}
   * @category Input element
   */
  inputAlign: null,
  /**
   * A list of property names to be set in the underlying input element from properties
   * by the same name in this Field object if the value is not `== null`.
   * @private
   * @category Input element
   */
  attributes: {
    value: [
      "placeholder",
      "ariaLabel",
      "autoComplete",
      "spellCheck",
      "minLength",
      "maxLength",
      "pattern",
      "tabIndex",
      "min",
      "max"
    ],
    $config: {
      merge: "distinct"
    }
  },
  nullValue: {
    $config: null,
    value: null,
    default: null
    // store _value=null on prototype
  },
  updatedClsDuration: 500,
  inputReadOnly: false,
  ariaElement: "input",
  /**
   * The tab index of the input field or fields, or `null` for natural tab order (recommended). Setting to `0`
   * is equivalent to natural tab order, but is unnecessary for fields since they are naturally tabbable
   * (i.e., accessible via the TAB key). Setting to `-1` disables tabbability but allows for focus to be set
   * to the element programmatically.
   *
   * From [MDN documentation](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/tabindex):
   *
   * **Warning**: You are recommended to only use `0` and `-1` as tabindex values. Avoid using tabindex values
   * greater than 0 and CSS properties that can change the order of focusable HTML elements ([Ordering flex
   * items](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_flexible_box_layout/Ordering_flex_items)).
   * Doing so makes it difficult for people who rely on using keyboard for navigation or assistive technology
   * to navigate and operate page content. Instead, write the document with the elements in a logical sequence.
   *
   * @config {Number} tabIndex
   * @category Input element
   */
  /**
   * The configuration for additional items associated to this field. This is typically used to add contextual
   * fields related to a {@link Core.widget.Checkbox checkbox} or {@link Core.widget.Radio radio button}. See
   * these classes for examples of nested fields.
   *
   * This config can be provided as an array of widget config objects, an object with named widgets (see
   * {@link Core.widget.FieldContainer#config-namedItems}, or a config object for the whole
   * {@link Core.widget.FieldContainer field container}.
   *
   * To determine if the object is a `namedItems` object or a {@link Core.widget.FieldContainer field container}
   * config, the object is checked for either a `type` or an `items` property. If it has either of these
   * properties, it is a field container config object. Configuring the container is useful for applying
   * {@link Core.widget.FieldContainer#config-cls classes} or {@link Core.widget.FieldContainer#config-style styles}
   * to the container as a whole.
   *
   * For example, to add named items:
   * ```javascript
   *  new Checkbox({
   *      text : 'Separate shipping address',
   *      container : {
   *          address1 : {
   *              type : 'textfield'
   *          },
   *          address2 : {
   *              type : 'textfield'
   *          }
   *      }
   *  });
   * ```
   *
   * To style the container as well, move the items to the `items` property and add `cls`:
   * ```javascript
   *  new Checkbox({
   *      text : 'Separate shipping address',
   *      container : {
   *          cls   : 'address-form',
   *          items : {
   *              address1 : {
   *                  type : 'textfield'
   *              },
   *              address2 : {
   *                  type : 'textfield'
   *              }
   *          }
   *      }
   *  });
   * ```
   *
   * @config {Core.widget.FieldContainer}
   * @accepts {Object<string,ContainerItemConfig>|ContainerItemConfig[]|FieldContainerConfig}
   * @category Container
   */
  container: {
    $config: "nullify",
    value: null
  },
  /**
   * The default configuration for the {@link #config-container}.
   * @config {Core.widget.FieldContainer}
   * @accepts {Core.widget.FieldContainer|FieldContainerConfig}
   * @category Container
   * @internal
   */
  containerDefaults: {
    type: "fieldcontainer",
    syncableConfigs: {
      disabled: (field) => field.disabled
    },
    syncConfigTriggers: {
      disabled: 1
    }
  },
  /**
   * The config controls how the value of nested items are handled when a parent container gets or sets its
   * {@link Core.widget.Container#property-values}.
   *
   * The valid values for this config are:
   *  - `null` (the default) will include the values of this field's items if this field stores its own value.
   *  - `true` to always include the values of this field's items.
   *  - `false` to never include the values of this field's items.
   *  - `'nested'` to include the values of this field's items as a nested object under the field's `name`.
   *    This field's `value` is stored as the `'value'` property of that object.
   *  - Any other string is treated as the name of a property on this field. When truthy, the values of this
   *    field's items will be included.
   *  - A function can be supplied that must return a value given this field as its sole argument. If that
   *    value is truthy, this field's items will be included.
   *
   * @config {Boolean|String|Function}
   * @param {Core.widget.Field} field Field instance
   * @returns {Boolean}
   * @category Container
   */
  containValues: null,
  /**
   * Set this config to `true` to always display items horizontally along with this field. This assigns an
   * {@link Core.widget.layout.Box hbox} as the {@link Core.widget.Container#config-layout} to the
   * {@link #config-container}.
   *
   * Alternatively, set this config to `false` to wrap this field's items below. This assigns a
   * {@link Core.widget.layout.VBox} as the {@link Core.widget.Container#config-layout} to the
   * {@link #config-container}.
   *
   * This config defaults to `true` if there is exactly one item, and `false` otherwise.
   * @config {Boolean}
   * @category Container
   */
  inline: null,
  testConfig: {
    updatedClsDuration: 10
  },
  keyMap: {},
  twinSyncConfigs: ["value"],
  /**
   * If you need to use something else than a default `input` element, as the input element, provide the tag
   * name here. Please note that this is used for advanced usage only, for example when using WebComponents
   * (custom elements), and that the configured element must fulfil the same contract as a regular input
   * element.
   * @config {String}
   * @category Input element
   * @advanced
   */
  inputTag: {
    $config: null,
    value: "input",
    default: "input"
  }
});
var Field = _Field;
var _a2;
Field.Trigger = (_a2 = class extends Widget {
  static get factoryable() {
    return {
      defaultType: Field.Trigger,
      extends: null
    };
  }
  get field() {
    return this.parent;
  }
  construct() {
    super.construct(...arguments);
    if (this.key) {
      this.keyMap = {
        [this.key]: "onClick"
      };
    }
  }
  compose() {
    return {
      "aria-hidden": true,
      class: {
        [`b-align-${this.align || "end"}`]: 1,
        "b-icon": 1
      },
      // eslint-disable-next-line bryntum/no-listeners-in-lib
      listeners: {
        click: "onClick",
        mousedown: "onMousedown"
      }
    };
  }
  changeAlign(align) {
    return align;
  }
  onClick(e) {
    const me = this, { field } = me;
    if (!(field.disabled || field.readOnly)) {
      if (e.key) {
        e.preventDefault();
      }
      if (field.trigger("trigger", { trigger: me }) !== false && me.handler) {
        field._isUserAction = true;
        field.callback(me.handler, field, [e, me]);
        field._isUserAction = false;
      }
    }
  }
  onMousedown(e) {
    const field = this.field, isKeyEvent = "key" in e;
    if (!isKeyEvent && DomHelper.isTouchEvent) {
      if (field.editable) {
        field.editable = false;
        field.setTimeout(() => field.editable = true, 500);
      }
    }
    e.preventDefault();
    if (DomHelper.getActiveElement(field.input) !== field.input) {
      field.focus();
    }
  }
}, __publicField(_a2, "$name", "FieldTrigger"), // Factoryable type name
__publicField(_a2, "type", "trigger"), // Align is a simple string at this level
__publicField(_a2, "configurable", {
  align: null,
  weight: null,
  role: null
}), _a2);
var _a3;
Field.SpinTrigger = (_a3 = class extends Field.Trigger {
  compose() {
    return {
      children: {
        upButton: {
          class: {
            "b-icon": 1,
            "b-spin-up": 1
          }
        },
        downButton: {
          class: {
            "b-icon": 1,
            "b-spin-down": 1
          }
        }
      }
    };
  }
  updateRepeat(repeat) {
    var _a5;
    (_a5 = this.clickRepeater) == null ? void 0 : _a5.destroy();
    this.clickRepeater = repeat ? ClickRepeater.new({
      element: this.element
    }, repeat) : null;
  }
  onClick(e) {
    const me = this, { field } = me;
    field._isUserAction = true;
    if (!(field.disabled || field.readOnly)) {
      if (e.target === me.upButton) {
        field.doSpinUp(e.shiftKey);
      } else if (e.target === me.downButton) {
        field.doSpinDown(e.shiftKey);
      }
    }
    field._isUserAction = false;
  }
  get isFocusable() {
    return !this.disabled && super.isFocusable;
  }
}, __publicField(_a3, "$name", "SpinTrigger"), // Factoryable type name
__publicField(_a3, "type", "spintrigger"), __publicField(_a3, "configurable", {
  repeat: {
    $config: ["nullify"],
    value: true
  }
}), _a3);
Field.Trigger.initClass();
Field.SpinTrigger.initClass();
Widget.register(Field.Trigger, "trigger");
Widget.register(Field.SpinTrigger, "spintrigger");
Field._$name = "Field";

// ../Core/lib/Core/widget/layout/Fit.js
var Fit = class extends Layout {
};
__publicField(Fit, "$name", "Fit");
__publicField(Fit, "type", "fit");
__publicField(Fit, "configurable", {
  containerCls: "b-fit-container",
  itemCls: "b-fit-item"
});
Fit.initClass();
Fit._$name = "Fit";

// ../Core/lib/Core/widget/List.js
var itemRange = document.createRange();
var List = class extends Widget {
  //endregion
  //region Events
  /**
   * User activated an item in the list either by pointer or keyboard.
   * The active record, list item index, and the triggering event are passed.
   * @event item
   * @param {Core.widget.List} source The List instance
   * @param {Core.data.Model} record Activated record
   * @param {Number} index List item index
   * @param {Event} event Triggering event
   */
  /**
   * User going to activate an item in the list either by pointer or keyboard.
   * The active record, list item index, and the triggering event are passed.
   * It is preventable by returning `false`
   * @event beforeItem
   * @preventable
   * @param {Core.widget.List} source The List instance
   * @param {Core.data.Model} record Activated record
   * @param {Number} args List item index
   * @param {Event} event Triggering event
   */
  /**
   * Fired when selection changes
   * @event selectionChange
   * @param {Core.widget.List} source The List instance
   * @param {Core.data.Model[]} selected An array of the currently selected records
   */
  //endregion
  construct(config, ...args) {
    var _a5;
    const me = this;
    if (Array.isArray(config)) {
      config = {
        items: config
      };
    }
    super.construct(config, ...args);
    const { element } = me, { classList } = element;
    if (me.multiSelect) {
      classList.add("b-multiselect");
    }
    if ((_a5 = me.store) == null ? void 0 : _a5.count) {
      me.refresh();
    } else {
      classList.add("b-empty");
    }
    EventHelper.on({
      element,
      click: "onClick",
      thisObj: me
    });
  }
  doDestroy() {
    var _a5;
    this.detachListeners("store");
    (_a5 = this.navigator) == null ? void 0 : _a5.destroy();
    super.doDestroy();
  }
  changeScrollable(scrollable) {
    scrollable = super.changeScrollable(scrollable);
    if (scrollable && this.title) {
      scrollable.stickies = ".b-list-title";
    }
    return scrollable;
  }
  onFocusIn(e) {
    var _a5;
    this.restoreActiveItem((_a5 = e._target) == null ? void 0 : _a5.closest(this.itemSelector));
    super.onFocusIn(e);
  }
  //region Virtualization
  get isVirtualized() {
    return typeof this.virtualize === "number" ? this.store.count > this.virtualize : Boolean(this.virtualize);
  }
  outOfViewTpl(record, i) {
    return `<li class="b-list-item b-out-of-view" data-index="${i}" aria-posinset="${i + 1}" aria-setsize="${this.store.count}" data-id="${StringHelper.encodeHtml(record.id)}"></li>`;
  }
  onItemIntersection(itemElement, isIntersecting) {
    if (isIntersecting) {
      const me = this, index = parseInt(itemElement.dataset.index), item = me.store.getAt(index);
      if (item) {
        DomHelper.sync(me.callback(me.itemWrapperTpl, me, [item, index, me]), itemElement);
      }
      me.itemIntersectionObserver.unobserve(itemElement);
    }
  }
  //endregion
  contentTpl() {
    const me = this, initiallyVisibleItems = Math.ceil(me.height / me.listItemHeight), allRecordsTpl = me.store.records.map((record, i) => !me.isVirtualized || i < initiallyVisibleItems ? me.callback(me.itemWrapperTpl, me, [record, i, me]) : me.outOfViewTpl(record, i));
    if (me.multiSelect && me.selectAllItem !== false) {
      allRecordsTpl.unshift(me.selectAllItemTpl());
    }
    if (me.title) {
      const titleId = `${me.id}-title`;
      allRecordsTpl.unshift(`<li id="${titleId}" class="b-list-item b-list-title" data-noselect>${StringHelper.encodeHtml(me.title)}</li>`);
      me.element.setAttribute("aria-labelledby", titleId);
    } else {
      me.element.removeAttribute("aria-labelledby");
    }
    if (me.emptyText && me.store.count === 0) {
      allRecordsTpl.push(`<li class="b-list-item b-empty-text">${me.L(me.emptyText)}</li>`);
    }
    return TemplateHelper.tpl`${allRecordsTpl}`;
  }
  itemWrapperTpl(record, i, me) {
    const {
      selected,
      getItemStyle,
      store,
      tooltipTemplate
    } = me, tooltip = (tooltipTemplate == null ? void 0 : tooltipTemplate(record)) || "", styles2 = [
      ...store.excludeCollapsedRecords ? [`--tree-level:${record.childLevel || 0}`] : [],
      ...getItemStyle ? [me.callback(getItemStyle, me, [record])] : []
    ].join(";"), style = styles2.length ? `style="${styles2}"` : "", isSelectable = me.callback(me.isSelectable, me, [record]);
    if (store.isGrouped && !record.isGroupHeader && record.instanceMeta(store).groupParent.meta.collapsed) {
      return "";
    }
    if (!record.ancestorsExpanded(store)) {
      return "";
    }
    return TemplateHelper.tpl`<li ${style} aria-posinset="${i + 1}" aria-setsize="${this.store.count}" class="${me.getItemClasses(record, i)}" role="option" ${tooltip ? `data-btip="${StringHelper.encodeHtml(tooltip)}"` : ""} aria-selected="${selected.includes(record)}" data-index="${i}" ${isSelectable ? "" : "data-noselect"} data-id="${StringHelper.encodeHtml(record.id)}" ${me.itemsFocusable ? 'tabindex="-1"' : ""}>${me.itemContentTpl(record, i)}</li>`;
  }
  itemContentTpl(record, i) {
    const me = this;
    if (record.isGroupHeader || record.isParent && me.store.excludeCollapsedRecords !== false) {
      let result = me.groupHeaderTpl(record, record.isParent ? me.callback(me.itemTpl, me, [record]) : StringHelper.capitalize(record.meta.groupRowFor));
      if (me.collapsibleGroups || record.isParent) {
        const isCollapsed = record.isParent ? record.instanceMeta(me.store).collapsed : record.meta.collapsed, arrowDirection = isCollapsed ? "right" : "down";
        result += `<button aria-label="${me.L("L{PanelCollapser." + (isCollapsed ? "Expand" : "Collapse") + "}")}" class="b-button b-tool b-icon b-icon-collapse-${arrowDirection} b-list-expander-icon" data-noselect></button>`;
      }
      return result;
    }
    return `${me.itemIconTpl(record, i)}${me.callback(me.itemTpl, me, [record])}`;
  }
  itemIconTpl(record, i) {
    return this.multiSelect && this.callback(this.isSelectable, this, [record]) ? `<i aria-hidden="true" role="presentation" class="b-selected-icon b-icon ${this.selectIconCls}"></i>` : "";
  }
  selectAllItemTpl() {
    const selectedCls = this.allSelected ? "b-selected" : "";
    return `<li class="${this.itemCls} b-select-all-item ${selectedCls}" data-select-all data-id="select-all" ${this.itemsFocusable ? 'tabindex="-1"' : ""}>${this.itemIconTpl({})}<div>${this.L("L{selectAll}")}</div></li>`;
  }
  getItemClasses(record) {
    var _a5, _b2;
    const me = this, {
      getItemCls,
      store
    } = me, activeItem = (_a5 = me._navigator) == null ? void 0 : _a5.activeItem, isActive2 = (activeItem == null ? void 0 : activeItem.dataset.id) == record.id, groupHeaderCls = "groupRowFor" in record.meta ? "b-list-item-group-header" : store.excludeCollapsedRecords && record.isParent ? "b-list-item-tree-parent" : "", isSelected = me.selected.includes(record) || ((_b2 = record.groupChildren) == null ? void 0 : _b2.every((r) => me.selected.includes(r))), itemCls2 = getItemCls ? this.callback(getItemCls, this, [record]) : "";
    return `${me.itemCls} ${record.cls || ""} ${itemCls2} ${isSelected ? me.selectedCls : ""} ${isActive2 ? me.navigator.focusCls : ""} ${groupHeaderCls}`;
  }
  onBeforeStoreLoad() {
    this.mask(this.L("L{loading}"));
  }
  onAfterStoreRequest() {
    this.unmask();
  }
  onStoreChange({ source: store, action, records, record, changes, isCollapse }) {
    const me = this, { selected } = me;
    switch (action) {
      case "dataset": {
        selected.match(store.storage, !me._externallyOwnedSelection);
        break;
      }
      case "remove":
        if (!isCollapse) {
          selected.remove(records);
        }
        break;
      case "clear":
        me.deselectAll();
        break;
      case "update": {
        const { id: id2 } = changes;
        if (id2) {
          DomHelper.setAttributes(
            me.contentElement.querySelector(StringHelper.encodeAttributeSelector(`data-id`, id2.oldValue)),
            {
              "data-id": id2.value
            }
          );
        }
        me.refreshItem(record);
        return;
      }
    }
    me.refresh();
  }
  onStoreRefresh() {
    if (this._selected) {
      this.refresh();
    } else {
      this.getConfig("selected");
    }
  }
  refresh() {
    var _a5, _b2;
    const me = this;
    if (me.isVisible && me.store) {
      const activeItem = (_a5 = me.navigator) == null ? void 0 : _a5.activeItem;
      if (me.itemIntersectionObserver) {
        me.itemIntersectionObserver.disconnect();
        me.itemIntersectionObserver = null;
      }
      me.paintListener = null;
      const node = DomHelper.createElementFromTemplate(me.contentTpl(), { fragment: true });
      me.clearItems().insertNode(node);
      me.element.classList.toggle("b-virtualized", me.isVirtualized);
      if (me.isVirtualized) {
        me.itemIntersectionObserver = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            me.onItemIntersection(entry.target, entry.isIntersecting, entry);
          }
        });
        for (const itemElement of me.element.querySelectorAll(me.itemSelector)) {
          me.itemIntersectionObserver.observe(itemElement);
        }
        me.listItemHeight = (_b2 = me.element.children[0]) == null ? void 0 : _b2.clientHeight;
      }
      if (activeItem) {
        me.restoreActiveItem(activeItem);
      }
      me.refreshCount = (me.refreshCount || 0) + 1;
      me.element.classList.toggle("b-empty", me.store.count === 0);
      me.updateSelectAllState();
    } else if (!me.paintListener) {
      me.paintListener = me.once("paint", "refresh");
    }
  }
  clearItems() {
    const me = this, { contentElement } = me, firstItem = contentElement.querySelector(me.itemSelector), lastChild = contentElement.lastChild;
    if (firstItem) {
      const items2 = contentElement.querySelectorAll(`${me.itemSelector}:not(.b-mask)`);
      itemRange.setStartBefore(firstItem);
      itemRange.setEndAfter(items2[items2.length - 1]);
      itemRange.deleteContents();
    } else {
      if (lastChild) {
        itemRange.setStartAfter(lastChild);
        itemRange.setEndAfter(lastChild);
      } else {
        itemRange.setStart(contentElement, 0);
        itemRange.setEnd(contentElement, 0);
      }
    }
    return itemRange;
  }
  refreshItem(...records) {
    for (const record of records) {
      const me = this, item = me.getItem(record);
      if (item) {
        const index = me.store.indexOf(record), newItem = DomHelper.createElementFromTemplate(me.callback(me.itemWrapperTpl, me, [record, index, me]));
        DomHelper.sync(newItem, item);
      }
    }
  }
  /**
   * Returns the DOM element representing the passed record (or record id)
   * @returns {Element} The item element
   */
  getItem(recordOrId) {
    if (recordOrId != null) {
      if (typeof recordOrId === "number") {
        return this.contentElement.querySelector(`[data-index="${recordOrId < 0 ? this.store.count + recordOrId : recordOrId}"]`);
      } else if (recordOrId.nodeType === Element.ELEMENT_NODE) {
        recordOrId = recordOrId.dataset.id;
      }
      if ((recordOrId == null ? void 0 : recordOrId.id) != null) {
        recordOrId = recordOrId.id;
      }
      return this.contentElement.querySelector(StringHelper.encodeAttributeSelector(`data-id`, recordOrId));
    }
  }
  /**
   * Searches up from the specified element for a list item and returns the associated record.
   * @param {HTMLElement} element Element somewhere within a list item element
   * @returns {Core.data.Model} Record for the item
   */
  getRecordFromElement(element) {
    if (element.target) {
      element = element.target;
    }
    element = element.closest(this.itemSelector);
    return this.store.getAt(parseInt(element.dataset.index));
  }
  //region getters/setters
  /**
   * May be *set* as an array of Objects which are converted into records and used to create this
   * List's {@link #config-store}
   * @member {Object[]} items
   */
  updateItems(items2) {
    this.store.data = ArrayHelper.asArray(items2 != null ? items2 : []).map((item) => {
      if (typeof item === "string") {
        return { [this.displayField]: item };
      }
      return item;
    });
  }
  updateDisabled(disabled, was) {
    super.updateDisabled(disabled, was);
    this.navigator && (this.navigator.disabled = disabled);
  }
  updateLocalization() {
    super.updateLocalization();
    this.refresh();
  }
  updateTitle() {
    this.refresh();
  }
  changeSelected(selected, oldSelected) {
    const me = this, internalListeners = {
      change: "onInternalSelectionChange",
      thisObj: me
    };
    if (selected) {
      if (selected.isCollection) {
        me._externallyOwnedSelection = true;
        selected.ion(internalListeners);
      } else {
        me._externallyOwnedSelection = false;
        selected = ArrayHelper.asArray(selected).reduce((r, s) => {
          s = s.isModel ? s : me.store.getById(s);
          if (s && me.callback(me.isSelectable, me, [s])) {
            r.push(s);
          }
          return r;
        }, []);
        if (oldSelected) {
          oldSelected.splice(0, oldSelected.count, selected);
          selected = oldSelected;
          me.refresh();
        } else {
          const records = selected;
          selected = me._selected = new Collection({
            internalListeners
          });
          selected.add(records);
        }
      }
      if (selected !== oldSelected) {
        oldSelected == null ? void 0 : oldSelected.un(internalListeners);
      }
    } else {
      oldSelected == null ? void 0 : oldSelected.destroy();
    }
    return selected;
  }
  updateSelected(selected) {
    if (selected) {
      this.refresh();
    }
  }
  get itemSelector() {
    return `.${this.itemCls}`;
  }
  /**
   * Gets/sets the collection of selected records.
   *
   * When used as a setter, a record, or record `id` or array of same may be passed to
   * set the selected records.
   * @property {Core.util.Collection}
   * @accepts {Core.data.Model|Core.data.Model[]|String|String[]|Number|Number[]}
   */
  get selected() {
    return this._selected;
  }
  updateMultiSelect(value2, old) {
    const { _selected: selected } = this;
    if (!value2 && old && (selected == null ? void 0 : selected.count) > 1) {
      selected.suspendEvents();
      this.deselect(selected.allValues.slice(1));
      selected.resumeEvents();
    }
    if (!this.isConfiguring) {
      this.refresh();
    }
  }
  get store() {
    this.getConfig("items");
    return this._store;
  }
  changeStore(store) {
    const me = this;
    if (!(store instanceof Store)) {
      if (Array.isArray(store)) {
        store = {
          data: store
        };
      }
      store = new Store(store);
    }
    me.detachListeners("store");
    if (store) {
      const storeListeners = {
        name: "store",
        change: "onStoreChange",
        refresh: "onStoreRefresh",
        thisObj: me
      };
      if (store.readUrl) {
        storeListeners.beforeLoad = "onBeforeStoreLoad";
        storeListeners.afterRequest = "onAfterStoreRequest";
      }
      if (store.isGrouped) {
        me.element.classList.add("b-grouped");
        storeListeners.groupchange = "onStoreRefresh";
      }
      store.ion(storeListeners);
      if (store.tree) {
        store.traverse((n) => {
          if (n.isParent && !n.expanded) {
            store.onNodeExpand(n);
          }
        });
      }
    }
    return store;
  }
  updateStore(store) {
    if (store == null ? void 0 : store.records.length) {
      this.getConfig("selected");
    }
    this.refresh();
  }
  changeNavigator(navigator2, oldNavigator) {
    const me = this, { element } = me;
    if (element) {
      navigator2 = Navigator.reconfigure(oldNavigator, navigator2 ? Navigator.mergeConfigs({
        ownerCmp: me,
        disabled: me.disabled,
        target: element,
        keyEventTarget: element,
        activateOnMouseover: me.activateOnMouseover
      }, navigator2) : null, me);
      if ((navigator2 == null ? void 0 : navigator2.keyEventTarget) === element && !me.itemsTabbable) {
        element.tabIndex = 0;
      }
    }
    return navigator2;
  }
  get minAlignHeight() {
    const lastItem = this.element.lastElementChild, minHeight = this.minHeight;
    if (minHeight != null) {
      return lastItem ? Math.min(lastItem.offsetTop + lastItem.offsetHeight, minHeight) : 0;
    }
  }
  //endregion
  //region Hide/Show
  alignTo(...args) {
    super.minHeight = this.minAlignHeight;
    super.alignTo(...args);
  }
  hide(...args) {
    this.navigator && (this.navigator.activeItem = null);
    return super.hide(...args);
  }
  show() {
    const me = this, { refreshCount, navigator: navigator2 } = me, { previousActiveItem } = navigator2;
    super.minHeight = me._minHeight;
    const retVal = super.show(...arguments);
    if (me.refreshCount === refreshCount && me.containsFocus) {
      me.restoreActiveItem(previousActiveItem, true);
    }
    return retVal;
  }
  /**
   * Sets the passed record as the current focused record for keyboard navigation and selection purposes.
   * @param {Core.data.Model} activeItem The item to highlight as the active ("focused") item.
   * @param {String|Boolean} [block='nearest'] The block scroll specification to scroll
   * the item into view. Defaults to `'nearest'` which means a minimal scroll, only if
   * the item is actually out of view. Passing `true` is for when the List is aligned
   * to a picker and the selected item should be scrolled to be close to the picker,
   * however that is aligned. See `Combo.showPicker` for usage.
   * @internal
   */
  restoreActiveItem(activeItem = this.navigator.previousActiveItem, block = "nearest") {
    const me = this, { navigator: navigator2, lastAlignSpec } = me;
    if (activeItem != null) {
      if (typeof block !== "string") {
        const alignZone = lastAlignSpec ? lastAlignSpec.zone : 1;
        block = alignZone & 1 ? "nearest" : alignZone ? "start" : "end";
      }
      activeItem = me.getItem(activeItem);
      if (activeItem && !me.disabled) {
        me.scrollable.scrollIntoView(activeItem, {
          block
        });
        navigator2.activeItem = activeItem;
      }
    }
  }
  //endregion
  //region Events
  /**
   * Selects list items on click.
   * @private
   */
  onClick(event) {
    const itemElement = event.target.closest(this.itemSelector);
    if (itemElement) {
      this.onItemClick(itemElement, event);
    } else if (this.clearSelectionOnEmptySpaceClick) {
      this.deselectAll();
    }
  }
  onSpaceKey(event) {
    const nodeName = event.target.nodeName.toUpperCase();
    if (nodeName !== "INPUT" && nodeName !== "TEXTAREA" || event.target.readOnly) {
      this.onEnterKey(event);
    }
  }
  onEnterKey(event) {
    const active = this.navigator.activeItem;
    if (active && !this.disabled) {
      event.target = active;
      this.onItemClick(active, event);
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  }
  /**
   * Key events which are not navigation are delegated up to here by the Navigator
   * @private
   */
  onInternalKeyDown(event) {
    const me = this;
    switch (event.key) {
      case "ArrowLeft":
        me.collapseItem();
        break;
      case "ArrowRight":
        me.expandItem();
        break;
      case " ":
        me.onSpaceKey(event);
        break;
      case "Enter":
        me.onEnterKey(event);
        break;
    }
  }
  collapseItem() {
    const { activeItem } = this.navigator;
    if (activeItem) {
      this.toggleCollapse(this.getRecordFromElement(activeItem), true);
    }
  }
  expandItem() {
    const { activeItem } = this.navigator;
    if (activeItem) {
      this.toggleCollapse(this.getRecordFromElement(activeItem), false);
    }
  }
  //endregion
  onItemClick(item, event) {
    const me = this, { allowGroupSelect } = me, index = parseInt(item.dataset.index), record = me.store.getAt(index), isSpecialRow = record == null ? void 0 : record.isSpecialRow, isNoSelect = item.contains(event.target.closest("[data-noselect]"));
    if (me.disabled || me.trigger("beforeItem", { item, record, index, event, userAction: true }) === false) {
      return;
    }
    me._isUserAction = true;
    if (item.contains(event.target.closest("[data-select-all]"))) {
      me.onSelectAllClick(item, event);
    } else if (event.target.matches(".b-list-expander-icon")) {
      me.toggleCollapse(record);
      return;
    } else if (record && !isNoSelect && (!isSpecialRow || allowGroupSelect)) {
      const { selected } = me, { groupParent } = record.instanceMeta(me.store);
      if (me.multiSelect) {
        let isSelected = selected.includes(record), toAddRemove = record;
        if (me.toggleAllIfCtrlPressed && event.ctrlKey) {
          toAddRemove = me.store.records;
        } else if (allowGroupSelect && isSpecialRow) {
          isSelected = !record.groupChildren.some((rec) => !selected.includes(rec));
          item.classList.toggle("b-selected", !isSelected);
          toAddRemove = record.groupChildren;
        }
        selected[isSelected ? "remove" : "add"](toAddRemove);
        if (!isSpecialRow && groupParent && allowGroupSelect) {
          const groupItem = me.getItem(groupParent);
          groupItem.classList.toggle("b-selected", groupParent.groupChildren.every((r) => selected.includes(r)));
        }
      } else {
        selected.splice(0, selected.count, record);
      }
    }
    if (me.isDestroyed) {
      return;
    }
    me.lastClicked = record;
    me.trigger("item", {
      item,
      record,
      index,
      event,
      userAction: true
    });
    me._isUserAction = false;
  }
  /**
   * Handles items being added or removed from the selected Collection
   * @param {Object} changeEvent
   * @private
   */
  onInternalSelectionChange({ action, removed, added, replaced }) {
    const me = this, {
      selectedCls,
      unselectedCls
    } = me;
    let record, item;
    if (action === "clear") {
      for (item of me.element.querySelectorAll(`.${selectedCls}`)) {
        item.classList.remove(selectedCls);
        item.setAttribute("aria-selected", false);
        unselectedCls && item.classList.remove(unselectedCls);
      }
    } else if (action !== "replaceValues") {
      for (record of removed) {
        item = me.getItem(record);
        if (item) {
          item.classList.remove(selectedCls);
          item.setAttribute("aria-selected", false);
        }
      }
      for (record of added) {
        item = me.getItem(record);
        if (item) {
          item.classList.add(selectedCls);
          item.setAttribute("aria-selected", true);
        }
      }
    }
    me.selectAllItem && me.updateSelectAllState();
    me.trigger("selectionChange", { selected: me.selected.values });
  }
  onSelectAllClick(item) {
    const checked = item.classList.contains(this.selectedCls);
    if (checked) {
      this.deselectAll();
    } else {
      this.selectAll();
    }
  }
  updateSelectAllState() {
    var _a5;
    (_a5 = this.element.querySelector(".b-select-all-item")) == null ? void 0 : _a5.classList.toggle(this.selectedCls, this.allSelected);
  }
  updateAllowGroupSelect() {
    this.refresh();
  }
  /**
   * Yields `true` if all the available items are selected.
   * @property {Boolean}
   * @readonly
   */
  get allSelected() {
    const { selected, store } = this;
    return selected.count && (store.isFiltered ? store.records.every((r) => selected.includes(r)) : store.count === selected.count);
  }
  /**
   * Selects all items in this list.
   * @category Selection
   */
  selectAll(keyEvent) {
    const me = this;
    if (me.multiSelect || !keyEvent) {
      me.selected.add(me.store.records);
      if (keyEvent && !me.navigator.activeItem) {
        me.navigator.activeItem = me.getItem(-1);
      }
    }
  }
  /**
   * Deselects all selected items
   * @category Selection
   */
  deselectAll() {
    this.selected.clear();
  }
  /**
   * Selects the passed item(s).
   *
   * An item to select may be the `id` of a record in this List's {@link #config-store}, or
   * it may be an object with an `id` __property__ which is the `id` of a record in this List's
   * {@link #config-store} (For example one of the records).
   * @param {String|String[]|Number|Number[]|Object|Object[]} toSelect
   * @param {Boolean} [clearSelected=false] Pass `true` to *replace* the existing selection.
   * @category Selection
   */
  select(toSelect, clearSelected = false) {
    const { store, selected } = this, { modelClass } = store;
    toSelect = ArrayHelper.asArray(toSelect).reduce((v, c) => {
      c = store.getById(modelClass.asId(c));
      if (c) {
        v.push(c);
      }
      return v;
    }, []);
    if (clearSelected && selected.count) {
      selected.splice(0, selected.count, toSelect);
    } else {
      selected.add(toSelect);
    }
  }
  /**
   * Deselects the passed item(s).
   *
   * An item to deselect may be the `id` of a record in this List's {@link #config-store}, or
   * it may be an object with an `id` __property__ which is the `id` of a record in this List's
   * {@link #config-store} (For example one of the records).
   * @param {String|String[]|Number|Number[]|Object|Object[]} toSelect
   * @category Selection
   */
  deselect(toDeselect) {
    const { modelClass } = this.store;
    toDeselect = ArrayHelper.asArray(toDeselect).reduce((v, c) => {
      c = this.selected.get(modelClass.asId(c));
      if (c) {
        v.push(c);
      }
      return v;
    }, []);
    this.selected.remove(toDeselect);
  }
  /**
   * Toggles the collapsed state of a group header or tree parent node record
   * @param {Core.data.Model} record The group header or parent record
   * @param {Boolean} [collapse] Force collapse (true) or expand (false)
   * @fires toggleGroup
   */
  toggleCollapse(groupRecord, collapse) {
    const { store } = this;
    if (groupRecord.isGroupHeader) {
      collapse = collapse === void 0 ? !groupRecord.meta.collapsed : collapse;
      store[collapse ? "collapse" : "expand"](groupRecord);
      this.refresh();
      this.trigger("toggleGroup", { groupRecord, collapse });
    } else if (groupRecord.isParent) {
      collapse = collapse === void 0 ? !groupRecord.instanceMeta(store).collapsed : collapse;
      groupRecord.instanceMeta(store).collapsed = collapse;
      this.refresh();
      this.trigger("toggleNode", { record: groupRecord, collapse });
    }
  }
  updateCollapsibleGroups(value2, old) {
    if (old && !value2) {
      const { store } = this;
      store.groupRecords.forEach((record) => store.expand(record));
    }
    this.refresh();
  }
};
//region Config
__publicField(List, "$name", "List");
__publicField(List, "type", "list");
__publicField(List, "configurable", {
  itemCls: "b-list-item",
  selectedCls: "b-selected",
  selectIconCls: "b-icon-check",
  tag: "ul",
  /**
   * An array of Objects which are converted into records and used to create this
   * List's {@link #config-store}
   * @config {Object[]}
   */
  items: null,
  /**
   * The model field to render into each list item
   * @config {String}
   * @default
   */
  displayField: "text",
  /**
   * A {@link Core.data.Store} which provides the records which map to List items. Each record is passed through the
   * {@link #config-itemTpl} to produce the DOM structure of the List. May be generated from an array of {@link #config-items}.
   *
   * The store may also be configured as an array of records, or record data objects from which records may be constructed.
   * @prp {Core.data.Store}
   * @accepts {Object|Object[]|StoreConfig|Core.data.Store}
   */
  store: {
    value: {},
    $config: "lazy"
  },
  navigator: true,
  scrollable: {
    overflowX: false,
    overflowY: true
  },
  itemsFocusable: true,
  /**
   * Configure as `true` to allow multi select and add checkboxes to the items
   * @prp {Boolean}
   * @default
   */
  multiSelect: false,
  /**
   * Select/deselect all if `CMD`/`CTRL` is pressed when clicking
   * @prp {Boolean}
   * @default false
   */
  toggleAllIfCtrlPressed: false,
  /**
   * True to add a collapse icon to toggle groups being collapsed or expanded
   * @prp {Boolean}
   * @default false
   */
  collapsibleGroups: false,
  /**
   * Set to `true` to add a "Select all" item to the list to select/unselect all items at once.
   * Only applies when {@link #config-multiSelect} mode is enabled.
   * @config {Boolean|String}
   * @default false
   */
  selectAllItem: false,
  /**
   * Template function (or name of a template function), which, when passed a record, returns the textual HTML for
   * that item. Defaults to a function returning the value of the record´s {@link #config-displayField}
   *
   * @config {Function|String}
   * @param {Core.data.Model} record The record
   * @returns {String}
   */
  itemTpl(record) {
    let text = StringHelper.encodeHtml(record.getValue(this.displayField));
    if (text == null || text === "") {
      text = "&nbsp;";
    }
    return text;
  },
  /**
   * Template function to provide a tooltip (textual, will be automatically HTML-encoded) shown when hovering an item.
   *
   * @config {Function}
   * @param {Core.data.Model} record The record representing the item
   * @returns {String}
   */
  tooltipTemplate: null,
  /**
   * Configure this as a function or the name of a function, which when passed a record in the
   * list, returns a style string to apply to its list item.
   *
   * @config {Function|String} getItemStyle
   * @param {Core.data.Model} record The record
   * @returns {String}
   */
  getItemStyle: null,
  /**
   * Configure this as a function or the name of a function, which when passed a record in the
   * list, returns a CSS class name string to apply to its list item.
   *
   * @config {Function|String} getItemCls
   * @param {Core.data.Model} record The record
   * @returns {String}
   */
  getItemCls: null,
  /**
   * Template function which is passed a group record and the uppercased group field name. The text returned
   * will be rendered as the group header.
   *
   * @config {Function}
   * @param {Core.data.Model} record The record
   * @param {String} groupName The current group name
   * @returns {String}
   */
  groupHeaderTpl(record, groupName) {
    let name = StringHelper.encodeHtml(groupName);
    if (name == null || name === "") {
      name = "&nbsp;";
    }
    return `${(this.allowGroupSelect || record.isParent) && this.isSelectable(record) ? this.itemIconTpl(record) : ""}${name}`;
  },
  /**
   * Configure as `true` to allow selecting groups (all the group child records will be toggled). Only
   * applicable when the store is grouped.
   * @config {Boolean}
   * @default
   */
  allowGroupSelect: true,
  /**
   * Configure as `true` to clear selection when clicking on empty space inside the List´s element.
   * @config {Boolean}
   * @default false
   */
  clearSelectionOnEmptySpaceClick: false,
  /**
   * A {@link Core.util.Collection}, or Collection config object to use to contain this List's selected
   * records.
   *
   * Or, an array encapsulating the initial selection which this List is to have upon Store load. This may be
   * an array of `id`s , or an array of objects with an `id` property:
   *
   * ```javascript
   * new List({
   *     // initially select record IDs 1 and 5 when store loads
   *     selected : [1, 5]
   * });
   * ```
   * @config {Object[]|Number[]|String[]|Core.util.Collection|CollectionConfig}
   */
  selected: {
    $config: ["nullify", "lazy"],
    value: []
  },
  /**
   * Configure as `true` to activate items on mouseover. This is used by the Combo
   * field when using a List as its dropdown.
   * @config {Boolean}
   */
  activateOnMouseover: null,
  role: "listbox",
  keyMap: {
    "Ctrl+a": "selectAll"
  },
  /**
   * The title to show at the top of the list.
   * @prp {String}
   */
  title: null,
  /**
   * Virtualize the list using an IntersectionObserver. Initially it will render all items in the list empty,
   * and instead fully populate them as they come into view.
   *
   * Accepts a boolean to enable/disable virtualization, or a threshold value over which virtualization will
   * be enabled.
   *
   * {@note}
   * Note that the overhead from virtualization is most of the time not worth it. Only enable it if you have
   * really large lists with complex item templates.
   * {/@note}
   *
   * @config {Boolean|Number}
   * @default false
   * @internal
   */
  virtualize: null,
  /**
   * Minimum estimated height of an item in the list. This value is used to estimate how many elements are
   * initially visible when the list is rendered, so initial render with virtualization turned on also displays
   * elements, and is not empty.
   *
   * @config {Number}
   * @default 1
   * @internal
   */
  listItemHeight: 10,
  /**
   * A function, or the name of a function in the ownership hierarchy which is used to determine
   * whether a record is selectable. By default, all records are selectable except group header
   * records in a grouped store.
   * @config {Function|String}
   * @returns {Boolean} `true` if the record is selectable
   */
  isSelectable: () => true,
  /**
   * The text to show when the list is empty. If not set, the list will be empty.
   * @prp {String}
   */
  emptyText: null
});
List.initClass();
List.prototype.navigatorClass = Navigator;
List._$name = "List";

// ../Core/lib/Core/widget/mixin/FormulaField.js
var returnTrue2 = () => true;
var fieldSorter = (a, b) => a.text.localeCompare(b.text);
var FormulaField_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    onFocusIn() {
      var _a6;
      super.onFocusIn(...arguments);
      if (this.formulaProvider) {
        (_a6 = this.fieldsList) == null ? void 0 : _a6.show();
      }
    }
    onFocusOut() {
      var _a6;
      (_a6 = this.fieldsList) == null ? void 0 : _a6.hide();
      super.onFocusOut(...arguments);
    }
    internalOnKeyEvent(event) {
      const me = this, { key } = event, { fieldsList } = me;
      if (event.type === "keydown") {
        if (event.key !== "Enter") {
          me.lastKey = event.key;
        }
        if (key === "Escape" && (fieldsList == null ? void 0 : fieldsList.isVisible)) {
          event.stopImmediatePropagation();
          fieldsList.hide();
          return;
        } else if (me.formulaPrefix && key === "ArrowDown") {
          event.preventDefault();
          if (!(fieldsList == null ? void 0 : fieldsList.isVisible)) {
            me.showFieldsList();
          }
        }
      }
      super.internalOnKeyEvent(...arguments);
    }
    internalOnInput() {
      super.internalOnInput(...arguments);
      this.checkForFormula();
    }
    updateValue() {
      super.updateValue(...arguments);
      this.checkForFormula();
    }
    checkForFormula() {
      var _a6, _b2;
      this.formulaPrefix = ((_a6 = this.dataField) == null ? void 0 : _a6.inputPrefixRE) ? (_b2 = this.value.match(this.dataField.inputPrefixRE)) == null ? void 0 : _b2[1] : null;
    }
    updateFormulaPrefix(prefix, was) {
      var _a6, _b2, _c2;
      const me = this;
      me.formulaProvider = prefix && ((_b2 = (_a6 = me.dataField) == null ? void 0 : _a6.getFormulaProvider) == null ? void 0 : _b2.call(_a6, prefix));
      was && me.input.classList.toggle(`b-${was.toLowerCase()}-input`, false);
      if (me.formulaProvider) {
        me.input.classList.toggle(`b-${prefix.toLowerCase()}-input`, true);
        me.formulaProvider.setConfig({
          inputField: me,
          store: me.formulaStore
        });
        me.whenVisible("showFieldsList");
      } else {
        (_c2 = me.fieldsList) == null ? void 0 : _c2.hide();
      }
    }
    /**
     * The formula typed between the parentheses in a `=XXX(...)` expression in the field's value if the `XXX`
     * matches an available {@link Core.util.FormulaProvider}.
     * @property {String}
     */
    get formula() {
      const { formulaProvider, value: value2 } = this;
      if (formulaProvider) {
        const { formulaRE } = formulaProvider;
        return formulaRE ? value2.match(formulaRE) : value2;
      }
      return "";
    }
    showFieldsList() {
      var _a6, _b2;
      const me = this, { input } = me, name = (_a6 = me.name) == null ? void 0 : _a6.replace(/^#/, ""), grid = me.up("gridbase", true);
      let picker = me.fieldsList;
      if (picker) {
        picker.owner = me;
        picker.widgetMap.fields.navigator.keyEventTarget = input;
      } else {
        me.fieldFilter = name && new CollectionFilter(({ id: id2 }) => id2 !== name);
        const { fieldNames } = me;
        picker = me.fieldsList = new Panel({
          title: "Fields",
          cls: "b-formula-fieldlist",
          floating: true,
          forElement: me.inputWrap,
          scrollAction: "realign",
          owner: me,
          autoShow: false,
          align: {
            matchSize: "min",
            align: "t0-b0",
            axisLock: true,
            minHeight: Math.min(3, fieldNames.length) * 40
          },
          layoutStyle: {
            padding: 0
          },
          layout: "fit",
          items: {
            fields: {
              type: "list",
              items: fieldNames,
              itemsFocusable: false,
              scrollable: {
                overflowY: true
              },
              navigator: {
                keyEventTarget: input,
                activateOnMouseover: true
              },
              getItemCls(record) {
                if (record.id === "this" || record.id === "data") {
                  return "b-special-field";
                }
              },
              onItem({ event, record }) {
                if (event.type === "click") {
                  me.lastKey = null;
                }
                if (!event.key || event.key === "Enter" && (me.lastKey === "ArrowDown" || me.lastKey === "ArrowUp")) {
                  me.selectField(record.id);
                } else {
                  event.stopImmediatePropagation = () => delete event.stopImmediatePropagation;
                }
              }
            }
          }
        });
        if ((_b2 = me.owner) == null ? void 0 : _b2.cellEditorContext) {
          grid.ion({
            cellMousedown: "onCellMousedown",
            cellClick: "onCellClick",
            thisObj: me,
            prio: 99999
          });
          me.record = me.owner.cellEditorContext.record;
        } else if (!me.record) {
          me.record = me.formulaProvider.record;
        }
      }
      picker.show();
      picker.activeItem = null;
    }
    onCellMousedown({ event }) {
      if (this.fieldsList.isVisible) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }
    // Users can also inject a field variable by clicking a cell in that column, just like Excel
    onCellClick({ event, column: { field } }) {
      if (this.fieldsList.isVisible) {
        event.stopImmediatePropagation();
        this.selectField(field);
        return false;
      }
    }
    // This inserts the selected field at the current caret position in the input
    selectField(fieldName) {
      var _a6, _b2, _c2;
      const me = this, { input } = me, value2 = input.value.trim(), { selectionStart } = input, atEnd = selectionStart >= value2.length, end = value2.endsWith(")") ? value2.length : value2.length + 2, length = ((_a6 = me.formulaProvider.prefix) == null ? void 0 : _a6.length) || 0;
      if (selectionStart > length + 1 && selectionStart < end) {
        const param = `${atEnd || ((_b2 = value2[selectionStart - 1]) == null ? void 0 : _b2.match(/\s|\(/)) ? "" : " "}$${fieldName}${atEnd || ((_c2 = value2[selectionStart]) == null ? void 0 : _c2.match(/\s|\)$/)) ? "" : " "}`;
        input.setRangeText(param, selectionStart, selectionStart);
        input.selectionStart = selectionStart + param.length;
        me.inputting = true;
        me.fetchInputValue();
        me.inputting = false;
      }
    }
    get fieldNames() {
      var _a6;
      const me = this;
      if (!me._fields) {
        const modelClass = ((_a6 = me.formulaStore) == null ? void 0 : _a6.modelClass) || me.record.constructor, { fieldMap } = modelClass, { fieldFilter } = me, fields = me._fields = modelClass.fields.map((field) => {
          const fieldName = typeof field === "string" ? field : field.name;
          return {
            id: fieldName,
            text: fieldMap[fieldName].label
          };
        }).filter(fieldFilter ? CollectionFilter.generateFiltersFunction([fieldFilter]) : returnTrue2).sort(fieldSorter);
        fields.push({
          text: "Record",
          id: "this"
        }, {
          text: "Store",
          id: "data"
        });
      }
      return me._fields;
    }
  }, __publicField(_a5, "$name", "FormulaField"), __publicField(_a5, "configurable", {
    /**
     * A {@link Core.data.field.DataField} which describes a field of a {@link Core.data.Model}
     * that we are editing.
     *
     * This is used to find {@link Core.data.field.DataField#config-formulaProviders}
     * which the field may support.
     *
     * ```javascript
     * items : {
     *     formulaField : {
     *         type      : 'textfield',
     *         dataField : myStore.modelClass.getFieldDefinition('name')
     *     }
     * }
     * ```
     * @config {Core.data.field.DataField}
     */
    dataField: null,
    /**
     * The Formula provider prefix currently being used
     * @property {String}
     * @private
     */
    formulaPrefix: null
  }), _a5;
};

// ../Core/lib/Core/widget/TextField.js
var TextField = class extends Field.mixin(FormulaField_default) {
  construct(config) {
    if ((config == null ? void 0 : config.inputType) === "hidden") {
      config.hidden = true;
    }
    super.construct(...arguments);
  }
  /**
   * Returns the current token - the token that the cursor is within or adjacent to in the
   * input field as separated by space, or the passed regular expression.
   * @param {RegExp} [delimiter=/\s/] The delimiter to use to separate tokens.
   * @returns {Object} The token object containing the token, start and end positions
   * and the cursor position.
   * @property {String} token The current token
   * @property {Number} start The start position of the token
   * @property {Number} end The end position of the token
   * @property {Number} cursor The cursor position
   * @category Field
   * @internal
   */
  getCurrentToken(delimiter = /\s/) {
    const { value: value2, selectionStart } = this.input;
    let start = selectionStart, end = selectionStart + 1;
    if (!value2.length || selectionStart == null) {
      return null;
    }
    while (start > 0 && !value2[start - 1].match(delimiter)) {
      start--;
    }
    while (end < value2.length && !value2[end].match(delimiter)) {
      end++;
    }
    return {
      token: value2.substring(start, end),
      cursor: selectionStart,
      start,
      end
    };
  }
};
__publicField(TextField, "$name", "TextField");
__publicField(TextField, "type", "textfield");
__publicField(TextField, "alias", "text");
__publicField(TextField, "configurable", {
  /**
   * The min number of characters for the input field
   * @config {Number} minLength
   * @category Field
   */
  /**
   * The max number of characters for the input field
   * @config {Number} maxLength
   * @category Field
   */
  nullValue: ""
});
TextField.initClass();
TextField._$name = "TextField";

// ../Core/lib/Core/widget/PickerField.js
var _PickerField = class _PickerField extends TextField {
  static get configurable() {
    return {
      /**
       * User can edit text in text field (otherwise only pick from attached picker)
       * @config {Boolean}
       * @default
       * @category Common
       */
      editable: true,
      /**
       * The name of the element property to which the picker should size and align itself.
       * @config {String}
       * @default element
       * @category Picker
       */
      pickerAlignElement: "inputWrap",
      // Does not get set, but prevents PickerFields inheriting value:'' from Field.
      value: null,
      triggers: {
        expand: {
          cls: "bars"
        }
      },
      /**
       * Configure as `true` to have the picker expand upon focus enter.
       * @config {Boolean}
       * @category Picker
       */
      autoExpand: null,
      /**
       * Configuration object for the {@link Core.widget.List picker} on initialization. Returns the
       * {@link Core.widget.List picker} instance at runtime.
       *
       * A config object which is merged into the generated picker configuration on initialization
       * to allow specific use cases to override behaviour.
       * For example:
       *
       * ```javascript
       *     picker: {
       *         align: {
       *             anchor: true
       *         }
       *     }
       * ```
       *
       * Returns the picker instance at runtime.
       *
       * @prp {Core.widget.Widget}
       * @accepts {Object}
       * @readonly
       * @category Picker
       */
      picker: {
        value: {
          floating: true
        },
        $config: ["lazy", "nullify"]
      },
      inputType: "text",
      // We need to realign the picker if we resize (eg a multiSelect Combo's ChipView wrapping)
      monitorResize: true,
      nullValue: null
    };
  }
  //endregion
  //region Init & destroy
  doDestroy() {
    var _a5;
    (_a5 = this.globalTapListener) == null ? void 0 : _a5.call(this);
    super.doDestroy();
  }
  changeEditable(editable) {
    if (BrowserHelper.isMobile && this.initialConfig.editable !== true) {
      editable = false;
    }
    return editable;
  }
  updateEditable(editable) {
    var _a5, _b2;
    const me = this, {
      element,
      ariaElement,
      nonEditableClickTarget
    } = me, { expand } = me.triggers, narrow = globalThis.matchMedia("(max-width: 410px)").matches;
    super.updateEditable(...arguments);
    element.classList.toggle("b-not-editable", !editable);
    (_a5 = me.globalTapListener) == null ? void 0 : _a5.call(me);
    (_b2 = me.nonEditableClickTargetListener) == null ? void 0 : _b2.call(me);
    if (editable === false) {
      if (BrowserHelper.isMobile && narrow) {
        expand == null ? void 0 : expand.hide();
      }
      ariaElement == null ? void 0 : ariaElement.removeAttribute("aria-autocomplete");
      if (nonEditableClickTarget) {
        me.nonEditableClickTargetListener = EventHelper.on({
          element: nonEditableClickTarget,
          click: (e) => {
            const target = nonEditableClickTarget.getRootNode().elementFromPoint(e.clientX, e.clientY);
            if (e.originalTarget === nonEditableClickTarget && target === nonEditableClickTarget) {
              me.onTriggerClick(e);
            }
          },
          thisObj: me
        });
      }
    } else {
      expand == null ? void 0 : expand.show();
      ariaElement == null ? void 0 : ariaElement.setAttribute("aria-autocomplete", "list");
      me.globalTapListener = GlobalEvents_default.ion({
        globaltap: "showKeyboard",
        thisObj: me
      });
    }
  }
  get nonEditableClickTarget() {
    return this.input;
  }
  get pickerAlignElement() {
    const { _pickerAlignElement } = this;
    return _pickerAlignElement.nodeType === Node.ELEMENT_NODE ? _pickerAlignElement : ObjectHelper.getPath(this, _pickerAlignElement);
  }
  //endregion
  //region Picker
  changePicker(picker, oldPicker) {
    const me = this;
    return _PickerField.reconfigure(oldPicker, picker ? Objects.merge({
      id: me.id + "-picker",
      owner: me,
      forElement: me.pickerAlignElement,
      align: {
        matchSize: picker.width == null,
        anchor: me.overlayAnchor,
        target: me.pickerAlignElement
      }
    }, picker) : null, me);
  }
  updatePicker(picker) {
    const me = this;
    me.detachListeners("pickerField.picker");
    if (picker) {
      picker.ion({
        name: "pickerField.picker",
        thisObj: me,
        show: "onPickerShow",
        hide: "onPickerHide"
      });
      DomHelper.setAttributes(me.ariaElement, {
        role: me.role,
        "aria-haspopup": picker.ownersRole,
        "aria-controls": picker.id,
        "aria-expanded": picker.isVisible
      });
    }
  }
  // Wait till the picker is shown before making this anything more than just a textbox input
  updateRole(role) {
    if (!this.isConfiguring || role === "textbox") {
      this.ariaElement.setAttribute("role", role);
    }
  }
  /**
   * Iterate over all widgets owned by this widget and any descendants.
   *
   * *Note*: Due to this method aborting when the function returns `false`, beware of using short form arrow
   * functions. If the expression executed evaluates to `false`, iteration will terminate.
   *
   * _Due to the {@link #config-picker} config being a lazy config and only being converted to be a
   * `List` instance just before it's shown, the picker will not be part of the iteration before
   * it has been shown once_.
   * @function eachWidget
   * @param {Function} fn A function to execute upon all descendant widgets.
   * Iteration terminates if this function returns `false`.
   * @param {Boolean} [deep=true] Pass as `false` to only consider immediate child widgets.
   * @returns {Boolean} Returns `true` if iteration was not aborted by a step returning `false`
   * @category Widget hierarchy
   */
  get childItems() {
    const result = super.childItems;
    if (this._picker) {
      result.push(this.picker);
    }
    return result;
  }
  //endregion
  //region Events
  /**
   * Check if field value is valid
   * @internal
   */
  onEditComplete() {
    super.onEditComplete();
    this.hidePicker();
  }
  onElementResize(resizedElement) {
    const me = this;
    if (me.pickerVisible) {
      me.picker.requestAnimationFrame(me.picker.realign, null, me.picker);
    }
    super.onElementResize(resizedElement);
  }
  /**
   * Allows using arrow keys to open/close list. Relays other keypresses to list if open.
   * @private
   */
  internalOnKeyEvent(event) {
    var _a5, _b2;
    const me = this;
    let callSuper = true;
    if (event.type === "keydown" && !me.disabled && !event.shiftKey && !event.ctrlKey) {
      if (me.pickerVisible) {
        const { picker } = me;
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopImmediatePropagation();
          me.hidePicker();
          callSuper = false;
        } else if (picker.onInternalKeyDown) {
          picker.onInternalKeyDown(event);
        } else if (event.key === "ArrowDown") {
          if (picker.focusable) {
            picker.focus();
          }
        }
      } else if (event.key === "ArrowDown" && me.picker && !((_b2 = (_a5 = me.triggers) == null ? void 0 : _a5.expand) == null ? void 0 : _b2.keyMap)) {
        me.onTriggerKeyDown(event);
      }
    }
    if (callSuper) {
      super.internalOnKeyEvent(event);
    }
  }
  onFocusIn(e) {
    var _a5;
    const me = this, target = (_a5 = GlobalEvents_default.currentPointerDown) == null ? void 0 : _a5.target;
    super.onFocusIn(e);
    if (me.autoExpand && !(target == null ? void 0 : target.matches(".b-fieldtrigger"))) {
      me.minChars = 0;
      me.onTriggerClick(e);
    }
  }
  /**
   * User clicked trigger icon, toggle list.
   * @private
   */
  onTriggerClick(event) {
    if (!this.disabled) {
      this.togglePicker("key" in event);
    }
  }
  onTriggerKeyDown(event) {
    if (this.trigger("triggerKey", { event }) !== false) {
      event.stopImmediatePropagation();
      event.preventDefault();
      this.onTriggerClick(event);
    }
  }
  /**
   * User clicked on an editable input field. If it's a touch event
   * ensure that the keyboard is shown.
   * @private
   */
  showKeyboard({ event }) {
    const input = this.input;
    if (DomHelper.isTouchEvent && DomHelper.getActiveElement(input) === input && event.target === input) {
      GlobalEvents_default.suspendFocusEvents();
      input.blur();
      input.focus();
      GlobalEvents_default.resumeFocusEvents();
    }
  }
  //endregion
  //region Toggle picker
  /**
   * Toggle the {@link #property-picker} visibility
   * @privateparam {Boolean} [focus] Pass `true` to focus the picker when it gets shown.
   * @category Picker
   */
  togglePicker(focus) {
    if (this.pickerVisible) {
      this.hidePicker();
    } else {
      this.showPicker(focus);
    }
  }
  /**
   * Show the {@link #property-picker}
   * @privateparam {Boolean} [focus] Pass `true` to focus the picker when it gets shown.
   * @category Picker
   */
  showPicker(focus) {
    var _a5;
    const me = this, { picker } = me;
    if (picker) {
      picker.show();
      (_a5 = me.ariaElement) == null ? void 0 : _a5.setAttribute("aria-expanded", true);
      if (picker.isVisible) {
        if (focus) {
          me.focusPicker();
        }
      }
    }
  }
  onPickerShow() {
    const me = this;
    if (me.pickerActiveDescendant) {
      me.ariaElement.setAttribute("aria-activedescendant", me.pickerActiveDescendant);
    }
    me.pickerVisible = true;
    me.element.classList.add("b-open");
    me.trigger("togglePicker", { show: true });
    me.pickerTapOutRemover = GlobalEvents_default.ion({
      globaltap: "onPickerTapOut",
      thisObj: me
    });
    me.pickerKeyDownRemover = EventHelper.on({
      element: me.picker.element,
      keydown: "onPickerKeyDown",
      thisObj: me
    });
  }
  onPickerHide() {
    var _a5, _b2;
    const me = this, { ariaElement } = me;
    me.pickerActiveDescendant = ariaElement == null ? void 0 : ariaElement.getAttribute("aria-activedescendant");
    ariaElement == null ? void 0 : ariaElement.removeAttribute("aria-activedescendant");
    ariaElement == null ? void 0 : ariaElement.setAttribute("aria-expanded", false);
    me.pickerVisible = false;
    me.element.classList.remove("b-open");
    me.trigger("togglePicker", { show: false });
    (_a5 = me.pickerTapOutRemover) == null ? void 0 : _a5.call(me);
    (_b2 = me.pickerKeyDownRemover) == null ? void 0 : _b2.call(me);
  }
  onPickerTapOut({ event }) {
    if (!this.containsFocus && !this.owns(event.target)) {
      this.hidePicker();
    }
  }
  onPickerKeyDown(event) {
    if (event.key === "Tab" && !this.picker.trapFocus) {
      const activeEl = DomHelper.getActiveElement(this.input), forwardedEvent = new KeyboardEvent("keydown", event);
      this.input.dispatchEvent(forwardedEvent);
      if (forwardedEvent.defaultPrevented) {
        event.preventDefault();
      }
      if (DomHelper.getActiveElement(this.input) === activeEl) {
        this.input.focus();
      } else {
        event.preventDefault();
      }
      if (!this.isDestroyed) {
        this.hidePicker();
      }
    }
  }
  //endregion
  //region Visibility
  /**
   * Hide picker
   * @category Picker
   */
  hidePicker() {
    if (this.pickerVisible) {
      this.picker.hide();
    }
  }
  focusPicker() {
  }
  focus() {
    const input = this.input;
    if (DomHelper.isTouchEvent && this.editable) {
      input.readOnly = true;
      this.setTimeout(() => input.readOnly = false, 500);
    }
    super.focus();
  }
  //endregion
};
//region Config
__publicField(_PickerField, "$name", "PickerField");
__publicField(_PickerField, "type", "pickerfield");
var PickerField = _PickerField;
PickerField._$name = "PickerField";

// ../Core/lib/Core/widget/ChipView.js
var ChipView = class extends List {
  itemContentTpl(record, i) {
    const me = this;
    return TemplateHelper.tpl`${me.iconTpl ? this.iconTpl(record) : ""}
            ${me.itemTpl(record, i)}
            ${me.closable ? '<div class="b-icon b-close-icon b-icon-clear" data-noselect></div>' : ""}`;
  }
  onInternalKeyDown(event) {
    const me = this, { selected } = me, { generation } = selected;
    if (me.closable && selected.count && (event.key === "Delete" || event.key === "Backspace")) {
      if (me.closeHandler) {
        me.callback(me.closeHandler, me.owner, [selected.values, { isKeyEvent: true }]);
      } else {
        me.store.remove(selected.values);
      }
      if (selected.generation !== generation) {
        event.stopImmediatePropagation();
      }
    } else {
      super.onInternalKeyDown(event);
    }
  }
  updateClosable(closable) {
    this.element.classList[closable ? "add" : "remove"]("b-chips-closable");
    if (!this.isConfiguring) {
      this.refresh();
    }
  }
  onClick(event) {
    const me = this, item = event.target.closest(`.${me.itemCls}`);
    if (me.closable && event.target.classList.contains("b-close-icon")) {
      const record = me.store.getAt(parseInt(item.dataset.index));
      if (me.closeHandler) {
        me.callback(me.closeHandler, me.owner, [[record]]);
      } else {
        me.store.remove(record);
      }
    } else {
      super.onClick(event);
    }
  }
};
//region Config
__publicField(ChipView, "$name", "ChipView");
__publicField(ChipView, "type", "chipview");
__publicField(ChipView, "configurable", {
  itemCls: "b-chip",
  /**
   * Configure as `true` to allow multi select and allow clicking and key navigation to select multiple chips.
   * @config {Boolean}
   * @default
   */
  multiSelect: false,
  /**
   * Configure as `true` to display a clickable close icon after the {@link Core.widget.List#config-itemTpl}.
   * When tapped, the configured {@link #config-closeHandler} is called passing the associated record.
   *
   * Chips may also be selected using the `LEFT` and `RIGHT` arrows (And the `Shift` key to do multiple,
   * contiguous selection). Pressing the `DELETE` or `BACKSPACE` key passes the selected records to the
   * {@link #config-closeHandler} (if not provided, the record representing the chip is removed from the store).
   *
   * @config {Boolean}
   * @default
   */
  closable: true,
  /**
   * A template function, which, when passed a record, returns the markup which encapsulates a chip's icon to be
   * placed before the {@link Core.widget.List#config-itemTpl}.
   * @config {Function}
   * @param {Core.data.Model} record The record to provide an icon for
   * @returns {DomConfig|String|null}
   */
  iconTpl: null,
  /**
   * If {@link #config-closable} is `true`, this is the name of a callback function to handle what the "close"
   * action means. If not provided, the record representing the chip is removed from the store.
   *
   * @config {String|Function}
   * @params {Core.data.Model[]} records Records to be closed
   * @params {Object} options Close options
   * @returns {void}
   */
  closeHandler: null
});
ChipView.initClass();
ChipView._$name = "ChipView";

// ../Core/lib/Core/widget/Combo.js
var errorFieldRequired = "L{fieldRequired}";
var errorValidateFilter = "L{validateFilter}";
var errorTooShort = "L{Field.tooShort}";
var errorRecordNotCommitted = "L{recordNotCommitted}";
var filterKeyTypes = /[\w\s]/;
var Combo = class extends PickerField {
  static get configurable() {
    return {
      /**
       * Optionally a {@link Core.util.CollectionFilter Filter} config object which the combo should use for
       * filtering using the typed value.
       * This may use a `filterBy` property to test its `value` against any field in the passed record.
       *
       * ```javascript
       * {
       *     type          : 'combo',
       *     store         : myStore,
       *     primaryFilter : {
       *         filterBy(record) {
       *             if (this.value == null) {
       *                 return true;
       *             }
       *             const value = this.value.toLowerCase();
       *
       *             // Match typed value with forename or surname
       *             return record.forename.toLowerCase().startsWith(value)
       *                 || record.surname.toLowerCase().startsWith(value);
       *         }
       *     }
       * }
       * ```
       *
       * @config {CollectionFilterConfig}
       */
      primaryFilter: {},
      /**
       * Configuration object for the {@link Core.widget.List picker} on initialization. Returns the
       * {@link Core.widget.List picker} instance at runtime.
       *
       * For example:
       *
       * ```javascript
       * new Combo({
       *     ...
       *     // configure the combobox picker
       *     picker : {
       *         listeners : {
       *             // prevent selection of item with id == 2
       *             beforeItem : ({ record }) => record.id !== 2
       *         }
       *     }
       * })
       * ```
       *
       * @prp {Core.widget.List}
       * @accepts {ListConfig}
       */
      picker: {
        type: "list",
        floating: true,
        scrollAction: "realign",
        itemsFocusable: false,
        align: {
          align: "t0-b0",
          axisLock: true
        },
        maxHeight: 324,
        scrollable: {
          overflowY: true
        },
        autoShow: false,
        focusOnHover: false
      },
      /**
       * Set to `true` to allow selection of multiple values from the dropdown list.
       *
       * Each value is displayed as a "Chip" to the left of the input area. Chips may be
       * selected using the `LEFT` and `RIGHT` arrow keys and deleted using the `DELETE` key
       * to remove values from the field. There is also a clickable close icon in each chip.
       *
       * Use {@link Core.widget.List#config-toggleAllIfCtrlPressed} to implement "select all" behaviour.
       *
       * ```javascript
       * {
       *     type   : 'combo',
       *     store  : myStore,
       *     picker : {
       *         toggleAllIfCtrlPressed : true
       *     }
       * }
       * ```
       *
       * @prp {Boolean}
       * @default false
       */
      multiSelect: null,
      /**
       * Rows to display in the dropdown (list items).
       *
       * If an object, the property names provide the {@link #config-value} for the Combo, and
       * the property values provide the displayed test in the list and input area eg:
       *
       *     items : {
       *         small  : 'Small',
       *         medium : 'Medium',
       *         large  : 'Large'
       *     }
       *
       * If an array, each entry may be
       *  - an object containing properties which must include
       * the {@link #config-valueField} and {@link #config-displayField} which populates the dropdown
       * with text and provides the corresponding field value.
       *  - An array whose first value provides the {@link #config-value} for the Combo and whose
       * second value provides the displayed test in the list and input area.
       *  - An array of values where the {@link #config-valueField} and {@link #config-displayField} are the same.
       *
       * eg:
       *
       *     items : [
       *         {value : 'small',  text : 'Small'},
       *         {value : 'medium', text : 'Medium'},
       *         {value : 'large',  text : 'Large'},
       *     ]
       *
       * or
       *
       *     items : [
       *         ['small',  'Small'],
       *         ['medium', 'Medium'],
       *         ['large',  'Large'],
       *     ]
       *
       * or
       *
       *     items : [ 'Small', 'Medium', 'Large' ]
       *
       * @config {Object[]|String[]|Object}
       */
      items: null,
      /**
       * Store used to populate items. Also accepts a Store config object
       * @prp {Core.data.Store|StoreConfig}
       */
      store: null,
      /**
       * Field used for item value when populating from store. Setting this to `null` will
       * yield the selected record as the Combo's {@link #property-value}.
       *
       * Leaving this value `undefined` means using the {@link Core.data.Model#property-idField-static} of the
       * {@link Core.data.Store#property-modelClass} of the store
       * as the value field.
       * @config {String|null}
       */
      valueField: void 0,
      /**
       * Field used for item text when populating from store
       * @config {String}
       * @default
       */
      displayField: "text",
      /**
       * Set to `true` to clear this field when user empties the input element
       * @config {Boolean}
       */
      clearWhenInputEmpty: null,
      /**
       * Width of picker, defaults to this combo's {@link #config-pickerAlignElement} width
       * @config {Number|String}
       */
      pickerWidth: null,
      /**
       * The minimum string length to trigger the filtering, only relevant when {@link #config-editable} is `true`.
       *
       * This defaults to `1` in the case of local filtering, but `4` if the
       * {@link #config-filterParamName} is set to cause remote dropdown loading.
       *
       * @config {Number}
       * @default
       */
      minChars: null,
      selected: null,
      /**
       * Template string used to render the list items in the dropdown list
       *
       * ```javascript
       * new Combo({
       *     listItemTpl : ({ text }) => `<div class="combo-color-box ${text}"></div>${text}`,
       *     editable    : false,
       *     items       : [
       *         'Black',
       *         'Green',
       *         'Orange',
       *         'Pink',
       *         'Purple',
       *         'Red',
       *         'Teal'
       *     ]
       * });
       * ```
       *
       * @config {Function}
       * @param {Core.data.Model} record The record representing the item being rendered
       * @typings record -> {Core.data.Model|Core.data.ComboModel}
       * @returns {String|null}
       */
      listItemTpl: null,
      /**
       * Template function that can be used to customize the displayed value
       * @param {Core.data.Model} record The record to provide a textual value for
       * @typings record -> {Core.data.Model|Core.data.ComboModel}
       * @param {Core.widget.Combo} combo A reference to this Combo
       * @config {Function}
       * @returns {String|null}
       */
      displayValueRenderer: null,
      /**
       * CSS class to add to picker
       * @config {String}
       */
      listCls: null,
      triggers: {
        expand: {
          cls: "b-icon-picker",
          handler: "onTriggerClick"
        }
      },
      /**
       * If the dropdown is to be populated with a filtered query to a remote server, specify the
       * name of the parameter to pass the typed string here. By default, the string is simply sent
       * as the value of the parameter. For special encoding, configure the combo with {@link #config-encodeFilterParams}
       * @config {String}
       */
      filterParamName: null,
      /**
       * A function which creates an array of values for the {@link #config-filterParamName} to pass
       * any filters to the server upon load.
       *
       * The default behaviour is just to set the parameter value to the filter's `value`,
       * but the filter can be fully encoded for example:
       *
       * ```javascript
       *    {
       *        encodeFilterParams(filters) {
       *            const result = [];
       *
       *            for (const { property, operator, value, caseSensitive } of filters) {
       *                result.push(JSON.stringify({
       *                    field : property,
       *                    operator,
       *                    value,
       *                    caseSensitive
       *                }));
       *           }
       *        return result;
       *    }
       * ```
       * @config {Function}
       * @param {Object[]} filters filters
       * @returns {Object[]} array of values
       */
      encodeFilterParams: (filters) => filters.map((f) => f.value),
      /**
       * If `false`, filtering will be triggered once you exceed {@link #config-minChars}. To filter only when
       * hitting Enter key, set this to `true`;
       * @config {Boolean}
       */
      filterOnEnter: false,
      /**
       * Configure as `true` to hide the expand trigger. This is automatically set to `true` if
       * remote filtering is enabled by setting the {@link #config-filterParamName} config.
       * @config {Boolean}
       * @default false
       */
      hideTrigger: null,
      /**
       * This implies that the picker will display an anchor pointer, but also means that the picker will align closer
       * to the input field so that the pointer pierces the {@link #config-pickerAlignElement}
       * @config {Boolean}
       * @default false
       */
      overlayAnchor: null,
      /**
       * The delay in milliseconds to wait after the last keystroke before filtering the list.
       *
       * This is a minimum of 300ms for remote filtering to keep network requests manageable, and
       * defaults to 10ms for locally filtered stores.
       * @config {Number}
       * @default
       */
      keyStrokeFilterDelay: null,
      defaultAction: "select",
      /**
       * How to query the store upon click of the expand trigger. Specify one of these values:
       *
       *  - `'all'` - Clear the filter and display the whole dataset in the dropdown.
       *  - `'last'` - Filter the dataset using the last filter value.
       *  - `null`/any other - Use the value in the input field to filter the dataset.
       *
       * @config {'all'|'last'|null}
       * @default
       */
      triggerAction: "all",
      /**
       * The name of an operator type as implemented in {@link Core.util.CollectionFilter#config-operator}
       * to use when filtering the dropdown list based upon the typed value.
       *
       * This defaults to `'startsWith'`, but the `'*'` operator may be used to match all
       * values which _contain_ the typed value.
       *
       * Not used when {@link #config-filterParamName} is set to cause remote dropdown loading.
       * The exact filtering operation is up to the server.
       *
       * @default
       * @prp {CollectionCompareOperator}
       */
      filterOperator: "startsWith",
      /**
       * Configure as `true` to force case matching when filtering the dropdown list based upon the typed value.
       *
       * @config {Boolean}
       * @default false
       */
      caseSensitive: false,
      /**
       * By default, the picker is hidden on selection in single select mode, and
       * remains to allow more selections when {@link #config-multiSelect} is `true`.
       * Setting this to a `Boolean` value can override that default.
       * @config {Boolean}
       */
      hidePickerOnSelect: null,
      /**
       * A config object to configure the {@link Core.widget.ChipView} to display the
       * selected value set when {@link #config-multiSelect} is `true`.
       *
       * For example the {@link Core.widget.List#config-itemTpl} or
       * {@link Core.widget.ChipView#config-iconTpl} might be configured to display
       * richer chips for selected items.
       * @config {ChipViewConfig}
       */
      chipView: {
        $config: ["lazy", "nullify"],
        value: {
          type: "combochipview"
        }
      },
      chipStore: {
        $config: ["lazy", "nullify"],
        value: {}
      },
      /**
       * When {@link #config-multiSelect} is `true`, you may configure `filterSelected` as
       * `true` to hide items in the dropdown when they are added to the selection.
       * It will appear as if the requested item has "moved" into the field's
       * {@link #config-chipView ChipView}.
       *
       * @config {Boolean}
       * @default false
       */
      filterSelected: null,
      /**
       * Text to display in the drop down when there are no items in the underlying store.
       *
       * Defaults to use the `noResults` localization string ("No results" in En locale).
       *
       * @config {String}
       */
      emptyText: "L{Combo.noResults}",
      /**
       * Get/sets combo value, selects corresponding item in the list
       * Setting null clears the field.
       *
       * If {@link #config-multiSelect} is `true`, then multiple values may be passed as an array.
       * If the values are records, these become the selected record set held by {@link #property-valueCollection},
       * and the `value` yielded by this field is an array of all the {@link #config-valueField}s from the records.
       * @fires select
       * @fires action
       * @member {Object|Number|String} value
       */
      /**
       * The initial value of this Combo box. In single select mode (default) it's a simple string value, for
       * {@link #config-multiSelect} mode, it should be an array of record ids.
       * @config {String|Number|String[]|Number[]}
       * @default
       */
      value: null,
      valueCollection: {
        $config: ["nullify", "lazy"],
        value: {}
      },
      /**
       * `true` to cause the field to be in an invalid state while the typed filter string does not match a record in the store.
       * @config {Boolean}
       * @default
       */
      validateFilter: true,
      /**
       * `true` to clear value typed to a multiselect combo when picker is collapsed
       * @config {Boolean}
       * @default
       */
      clearTextOnPickerHide: true,
      /**
       * Specify `false` to not clear value typed to a multiselect combo when an item is selected.
       * @config {Boolean}
       * @default
       */
      clearTextOnSelection: true,
      // set to `true` to destroy the old combo store when it is replaced with a new store
      destroyStore: false,
      /**
       * A key value which, when typed in a {@link #config-multiSelect} Combo, selects the
       * currently active item in the picker, and clears the input field ready for another
       * match to be typed.
       * @config {String}
       * @default
       */
      multiValueSeparator: ",",
      /**
       * If configured as `true`, this means that when an unmatched string is typed into the
       * combo's input field, and `ENTER`, or the {@link #config-multiValueSeparator} is typed,
       * a new record will be created using the typed string as the {@link #config-displayField}.
       *
       * If configured as a function, or the name of a function in the owning component hierarchy, the function
       * will be called passing the string and combo field instance and should return the record to add (if any).
       *
       * The new record will be appended to the store, and the value selected.
       *
       * If the Store is an {@link Core.data.AjaxStore}, the new record will be eiligible for
       * syncing to the database through its {@link Core.data.AjaxStore#config-createUrl createUrl}.
       *
       * If the `AjaxStore` is configured to {@link Core.data.AjaxStore#config-autoCommit autoCommit},
       * the record will be synced immediately. If the server does not accept the new addition,
       * the field is placed temporarily into an invalid state with a message that explains this.
       *
       * For example:
       *
       * ```javascript
       *     new Combo({
       *         label : 'Employee name',
       *         store : employees,
       *         createOnUnmatched(name, combo) {
       *             name = validateEmployeeName(name);
       *
       *             if (name) {
       *                 return new Employee({
       *                     name,
       *                     email : generateEmployeeEmail(name)
       *                 });
       *             }
       *             else {
       *                 combo.setError('Invalid new employee name');
       *             }
       *         }
       *     });
       * ```
       *
       * @config {Function|String|Boolean}
       * @param {String} name Record name
       * @param {Core.widget.Combo} combo Combo instance
       * @returns {Core.data.Model} New record
       */
      createOnUnmatched: null,
      role: "combobox",
      /**
       * Configure this as `true` to render the dropdown list as a permanently visible list
       * in the document flow immediately below the input area instead of as a popup.
       *
       * This also hides the expand trigger since it is not needed.
       * @config {Boolean}
       * @default false
       */
      inlinePicker: null,
      testConfig: {
        // So that locally filtered tests do not have to wait after type gestures.
        // Note that for remote filtering, we set a min of 300ms
        keyStrokeFilterDelay: 0,
        // So that test doesn't have to wait 2 seconds for the filter string to expire
        nonEditableFilterTimeout: 200
      },
      /**
       * Configure this as `true` and the items display field values will be localized. The display field values
       * need to be a locale string.
       * @config {Boolean}
       * @private
       * @default
       */
      localizeDisplayFields: false,
      /**
       * Provide a function that returns items to be shown in the combo's selector.
       * @config {Function}
       * @private
       */
      buildItems: null,
      /**
       * Configure this as `true` to reapply sorting after
       * {@link #config-localizeDisplayFields localizing the items}.
       * @config {Boolean}
       * @private
       * @default
       */
      sortItemsOnLocaleChange: false,
      /**
       * When the Combo is `editable : false`, keystrokes are listened for and a filter string built
       * which filters down the visible result. After a timeout of {@link #config-nonEditableFilterTimeout}
       * milliseconds, the filter string is cleared.
       * @prp {Number}
       * @default
       * @internal
       */
      nonEditableFilterTimeout: 2e3
    };
  }
  /**
   * A constant value for the {@link #config-triggerAction} config to indicate that clicking the trigger should
   * filter the dataset using the last filter query string, *not* the input field value.
   * @member {String} queryLast
   * @readonly
   * @static
   */
  static get queryLast() {
    return "last";
  }
  //endregion
  construct(config) {
    super.construct(...arguments);
    if (!this.store) {
      this.items = [];
    }
    if (this.filterOnEnter) {
      this.hideTrigger = true;
    }
  }
  startConfigure(config) {
    this.usingRecordAsValue = config.valueField === null;
    super.startConfigure(...arguments);
  }
  afterConfigure() {
    super.afterConfigure();
    const me = this;
    if (!ObjectHelper.hasOwn(me, "_value")) {
      me._value = me.nullValue;
      me._lastValue = me._lastValue || me._value;
    }
  }
  get childItems() {
    const { _chipView } = this, result = super.childItems;
    if (_chipView) {
      result.push(_chipView);
    }
    return result;
  }
  /**
   * Returns array of the Combo's inner HTML elements.
   * This getter can be overriden.
   *
   * @property {Array<HTMLElement|DomConfig>}
   * @getter
   * @readonly
   * @advanced
   */
  get innerElements() {
    var _a5;
    const chipViewElement = (_a5 = this._chipView) == null ? void 0 : _a5.element, { input, inputElement } = this;
    if (chipViewElement) {
      DomSync.sync({
        targetElement: input,
        domConfig: inputElement
      });
    }
    return [chipViewElement || inputElement];
  }
  updateInlinePicker(inlinePicker) {
    if (inlinePicker) {
      this.element.classList.add("b-inline-picker");
      this.getConfig("picker");
      this.triggers.expand = null;
      this.pickerVisible = true;
    }
  }
  hidePicker() {
    if (!this.inlinePicker) {
      return super.hidePicker(...arguments);
    }
  }
  changeChipStore(chipStore) {
    if (chipStore && !(chipStore instanceof Store)) {
      chipStore = new Store(Store.mergeConfigs({
        storage: this.valueCollection
      }, chipStore));
    }
    return chipStore;
  }
  updateChipStore(store, was) {
    was == null ? void 0 : was.destroy();
  }
  changeChipView(chipView, oldChipView) {
    const me = this;
    me.element.classList[chipView ? "add" : "remove"]("b-uses-chipview");
    if (chipView) {
      const { input } = me;
      if (!me.chipStore) {
        me.chipStore = {};
      }
      return ComboChipView.reconfigure(oldChipView, chipView, {
        defaults: {
          parent: me,
          insertBefore: input,
          store: me.chipStore,
          closable: !me.readOnly,
          label: me.label,
          ariaLabel: me.ariaLabel,
          navigator: {
            type: "combochipnavigator",
            disabled: me.readOnly,
            keyEventTarget: input
          }
        }
      });
    }
    oldChipView == null ? void 0 : oldChipView.destroy();
  }
  updateChipView(chipView) {
    var _a5;
    const me = this;
    me._chipViewEventDetacher = (_a5 = me._chipViewEventDetacher) == null ? void 0 : _a5.call(me);
    me.chipStore = chipView == null ? void 0 : chipView.store;
    if (chipView) {
      chipView.element.appendChild(me.input);
      me._chipViewEventDetacher = EventHelper.on({
        element: chipView.element,
        mousedown: "onChipViewMousedown",
        thisObj: me
      });
    }
  }
  updateMultiSelect(multiSelect, oldMultiSelect) {
    const me = this, {
      input,
      element
    } = me, fixValue = !me.isConfiguring;
    let { value: value2 } = me;
    element.classList[multiSelect ? "add" : "remove"]("b-multiselect");
    if (multiSelect) {
      const { chipView } = me, { parentNode } = input, chipViewEl = chipView == null ? void 0 : chipView.element;
      if (chipViewEl && chipViewEl !== parentNode) {
        parentNode.insertBefore(chipViewEl, input);
        chipViewEl.appendChild(input);
        me.chipView.refresh();
        element.classList.add("b-uses-chipview");
      }
      input.value = "";
      if (fixValue) {
        value2 = ArrayHelper.asArray(value2);
      }
    } else {
      const chipView = me._chipView, { parentNode } = input, chipViewEl = chipView == null ? void 0 : chipView.element;
      if (chipViewEl === parentNode) {
        chipViewEl.parentNode.insertBefore(input, chipViewEl);
        chipViewEl.remove();
        element.classList.remove("b-uses-chipview");
      }
      if (fixValue && typeof value2 !== "string") {
        value2 = (value2 == null ? void 0 : value2.length) ? value2[0] : null;
      }
    }
    if (fixValue) {
      me.value = value2;
    }
    if (!me.isConfiguring) {
      me.updateEditable(me.editable, null);
    }
  }
  onChipViewMousedown(mousedownEvent) {
    mousedownEvent.preventDefault();
    if (!this.containsFocus) {
      this.focus();
    }
  }
  onChipClose(records, options = {}) {
    if (options.isKeyEvent && this.input.value === "" || !options.isKeyEvent) {
      this._isUserAction = true;
      this.valueCollection.remove(records);
      this._isUserAction = false;
    }
  }
  updateFilterParamName(filterParamName) {
    if (this.hideTrigger !== false) {
      this.hideTrigger = Boolean(filterParamName);
    }
  }
  updateHideTrigger(hideTrigger) {
    this.element.classList[hideTrigger ? "add" : "remove"]("b-hide-trigger");
  }
  //region Getters/setters
  updateKeyStrokeFilterDelay(delay2) {
    this.filterOnInput.delay = delay2;
  }
  updateReadOnly(readOnly) {
    super.updateReadOnly(...arguments);
    this._chipView && (this._chipView.closable = !readOnly);
  }
  updateDisabled(disabled, was) {
    super.updateDisabled(disabled, was);
    this._chipView && (this._chipView.closable = !disabled);
  }
  updateFilterOperator(filterOperator) {
    if (this.primaryFilter) {
      this.primaryFilter.operator = filterOperator;
    }
  }
  get minChars() {
    var _a5;
    return (_a5 = this._minChars) != null ? _a5 : this.remoteFilter ? 4 : 1;
  }
  get validateFilter() {
    return this._validateFilter && !this.createOnUnmatched;
  }
  get items() {
    return this.store.allRecords;
  }
  updateBuildItems(fn2) {
    if (fn2) {
      this.items = fn2.call(this);
    }
  }
  /**
   * Prepares items to work in attached menu (converts strings to items)
   * @private
   */
  changeItems(items2) {
    const me = this;
    if (items2 == null) {
      if (me.store && !me.store.isItemStore) {
        return;
      }
      items2 = [];
    }
    if (me.buildItems && !(items2 == null ? void 0 : items2.length)) {
      items2 = me.buildItems();
    }
    if (items2 instanceof Store) {
      me.store = items2;
      return;
    }
    const noValueField = !("valueField" in me.initialConfig), displayField = me.displayField;
    let itemModel, valueField = me.valueField, storeData;
    if (Array.isArray(items2)) {
      storeData = items2.map((item) => {
        let result = item;
        if (item instanceof Model) {
          itemModel = item.constructor;
          if (noValueField && !valueField) {
            me.valueField = valueField = itemModel.idField;
          }
        } else {
          if (typeof item === "string" || typeof item === "number") {
            if (noValueField && !valueField) {
              me.valueField = valueField = me.displayField;
            }
            result = {
              [valueField]: item,
              [displayField]: item
            };
          } else {
            if (noValueField && !valueField) {
              me.valueField = valueField = "value";
            }
            if (Array.isArray(item)) {
              result = {
                [valueField]: item[0],
                [displayField]: item[1]
              };
            }
          }
        }
        if (item.selected) {
          me.value = result;
        }
        return result;
      });
    } else {
      if (noValueField && !valueField) {
        me.valueField = valueField = "value";
      }
      storeData = [];
      Object.entries(items2).forEach(([key, value2]) => {
        storeData.push({
          [valueField]: key,
          [displayField]: value2
        });
      });
    }
    const valueFieldDefinition = valueField === displayField ? {
      name: "value",
      dataSource: displayField
    } : valueField;
    if (!itemModel) {
      class ModelClass extends Model {
        static get idField() {
          return me.valueField;
        }
        static set idField(idField) {
          super.idField = idField;
        }
        static get fields() {
          const result = [valueFieldDefinition, displayField];
          if (me.localizeDisplayFields) {
            result.push("localeClass");
          }
          return result;
        }
      }
      itemModel = ModelClass;
    }
    if (me.store) {
      me.store.setConfig({
        modelClass: itemModel,
        data: storeData
      });
    } else {
      me.store = new Store({
        isItemStore: true,
        data: storeData,
        idField: valueField,
        // We frequently populate combos with data from other stores, don't want warnings for consuming local
        // records from those stores with generated ids
        verifyNoGeneratedIds: false,
        modelClass: itemModel,
        // We do not want to pollute configured items
        useRawData: false
      });
    }
  }
  get value() {
    const me = this, { valueCollection, valueField } = me;
    if (valueField == null) {
      return me.multiSelect ? valueCollection.values.slice() : valueCollection.first;
    }
    let value2;
    if (me.multiSelect) {
      value2 = valueCollection.count ? valueCollection.map((r) => r[valueField]) : me._unmatchedValue || [];
    } else {
      value2 = valueCollection.count ? valueCollection.first[valueField] : me._unmatchedValue || me.nullValue;
    }
    return value2;
  }
  set value(value2) {
    super.value = value2;
  }
  // Documented in superclass.
  get needsInputSync() {
    return this.usesChipView ? false : !this.editable;
  }
  changeValue(value2, oldValue) {
    var _a5, _b2;
    const me = this, { multiSelect } = me;
    me._unmatchedValue = null;
    if (value2 === oldValue) {
      me.syncInputFieldValue(true);
      return;
    }
    if (!multiSelect && Array.isArray(value2) && value2.length > 1) {
      throw new Error("Multiple values cannot be set to a non-multiSelect Combo");
    }
    me.triggerConfig("items");
    if (!me.inputting) {
      (_a5 = me._chipView) == null ? void 0 : _a5.selected.clear();
    }
    if (!me.store) {
      me.items = [];
    }
    const {
      valueField,
      displayField,
      nullValue,
      store,
      valueCollection,
      _picker
    } = me, { storage } = store, hidePicker = (_b2 = me.hidePickerOnSelect) != null ? _b2 : !multiSelect, isUserAction = me._isUserAction || (_picker == null ? void 0 : _picker._isUserAction) || hidePicker && me.pickerVisible || false;
    if (!(value2 == nullValue || value2 == null)) {
      let record;
      if (!me.remoteFilter && store.isAjaxStore && !store.count) {
        store.ion({ load: () => me.value = value2, once: true, thisObj: me });
        me._unmatchedValue = value2;
        return;
      }
      const arrayPassed = Array.isArray(value2), values = arrayPassed ? value2.slice() : [value2];
      if (me.remoteFilter) {
        if (ObjectHelper.isObject(value2) || value2.isModel) {
          if (!store.getById(value2.id)) {
            store.data = [value2];
            me.valueCollection.splice(0, me.valueCollection.count, store.first);
          } else {
            me.valueCollection.splice(0, me.valueCollection.count, store.getById(value2.id));
          }
        } else {
          const wasConfiguring = me.isConfiguring;
          me.primaryFilter.setConfig({
            value: value2,
            disabled: false
          });
          store.performRemoteFilter(true).then(() => {
            if (me.isDestroyed) {
              return;
            }
            const { isConfiguring } = me;
            me.isConfiguring = wasConfiguring;
            valueCollection.splice(0, valueCollection.count, store.allRecords);
            me.isConfiguring = isConfiguring;
          });
        }
        return;
      } else if (store.isFiltered) {
        if (store.isChained) {
          store.fillFromMaster();
        }
        me.primaryFilter.disabled = true;
        store.filter();
      } else if (store.isChained) {
        store.fillFromMaster();
      }
      for (let i = 0, len = values.length; i < len; i++) {
        let currentValue = values[i];
        if (currentValue instanceof Model) {
          if (!storage.includes(currentValue, true)) {
            store.add(currentValue);
          }
        } else {
          const isObject4 = ObjectHelper.isObject(currentValue);
          if (isObject4 && valueField) {
            currentValue = currentValue[store.modelClass.fieldMap[valueField].dataSource];
          }
          record = storage.getBy(displayField, currentValue) || storage.getBy(valueField, currentValue) || (valueCollection.getBy(displayField, currentValue) || valueCollection.getBy(valueField, currentValue));
          if (record instanceof Set) {
            record = [...record][0];
          }
          if (record) {
            if (isObject4) {
              record.set(values[i]);
            }
            values[i] = record;
          } else {
            values.splice(i, 1);
            len--;
            i--;
          }
        }
      }
      const noMatches = !values.length;
      const vcGen = valueCollection.generation;
      valueCollection.splice(0, valueCollection.count, values);
      if (noMatches) {
        me._value = arrayPassed && value2.length === 0 ? null : value2;
        me._unmatchedValue = me._value;
      }
      if (noMatches || valueCollection.generation === vcGen) {
        me.syncInputFieldValue();
      }
      me.syncEmpty();
      if (noMatches && !me.isConfiguring) {
        me.triggerFieldChange({
          value: value2,
          oldValue,
          userAction: isUserAction,
          valid: me.isValid
        });
      }
    } else {
      if (valueCollection.count) {
        valueCollection.clear();
      } else {
        const oldValue2 = me._value;
        me._value = null;
        me.syncInputFieldValue();
        me.syncEmpty();
        if (!me.isConfiguring) {
          me.triggerFieldChange({
            value: value2,
            oldValue: oldValue2,
            userAction: isUserAction,
            valid: me.isValid
          });
        }
      }
    }
    if (!me.remoteFilter) {
      me._lastValue = me._value;
    }
  }
  hasChanged(oldValue, newValue) {
    if (this.multiSelect) {
      return !ObjectHelper.isEqual(oldValue, newValue);
    }
    return super.hasChanged(...arguments);
  }
  onComboStoreChange({ action }) {
    if (action !== "filter") {
      this.syncInputFieldValue(true);
    }
  }
  syncInputFieldValue(skipHighlight) {
    var _a5;
    if (this.usesChipView) {
      (_a5 = this.chipView) == null ? void 0 : _a5.refresh();
    } else {
      super.syncInputFieldValue(skipHighlight);
    }
  }
  get usesChipView() {
    return Boolean(this.multiSelect && this._chipView);
  }
  /**
   * Returns `true` if this field has no selected records.
   * @property {Boolean}
   * @readonly
   */
  get isEmpty() {
    return this.valueCollection.count === 0 && !this._unmatchedValue;
  }
  get inputValue() {
    const me = this;
    let result = me.selected ? me.selected[me.displayField] : me.value;
    if (me.displayValueRenderer) {
      result = me.callback(me.displayValueRenderer, me, [me.selected, me]);
    }
    return result == null ? "" : result;
  }
  get nonEditableClickTarget() {
    var _a5;
    return this.multiSelect && ((_a5 = this.chipView) == null ? void 0 : _a5.element) || super.nonEditableClickTarget;
  }
  /**
   * A {@link Core/util/Collection} which holds the currently selected records
   * from the store which dictates this field's value.
   *
   * Usually, this will contain one record, the record selected.
   *
   * When {@link #config-multiSelect} is `true`, there may be several records selected.
   * @member {Core.util.Collection} valueCollection
   * @readonly
   */
  changeValueCollection(valueCollection, oldValueCollection) {
    oldValueCollection == null ? void 0 : oldValueCollection.destroy();
    if (valueCollection) {
      if (!valueCollection.isCollection) {
        valueCollection = new Collection({
          internalListeners: {
            noChange: "onValueCollectionNoChange",
            change: "onValueCollectionChange",
            prio: -1e3,
            // The ChipView must react to changes first.
            thisObj: this
          }
        });
      }
      return valueCollection;
    }
  }
  changePrimaryFilter(primaryFilter) {
    if (primaryFilter.isCollectionFilter) {
      primaryFilter.setConfig({
        disabled: true,
        property: this.displayField,
        operator: this.filterOperator,
        caseSensitive: this.caseSensitive
      });
    } else {
      if (typeof primaryFilter === "function") {
        primaryFilter = {
          filterBy: primaryFilter
        };
      }
      primaryFilter = new CollectionFilter({
        // Need an id to replace any existing combo filter on the store.
        id: "primary",
        disabled: true,
        property: this.displayField,
        operator: this.filterOperator,
        caseSensitive: this.caseSensitive,
        ...primaryFilter
      });
    }
    return primaryFilter;
  }
  changeStore(store) {
    const me = this, storeFilters = [], {
      valueCollection,
      keyStrokeFilterDelay,
      filterParamName
    } = me;
    if (Array.isArray(store)) {
      me.items = store;
      return;
    }
    let remoteFilter;
    if (store) {
      if (store.isStore) {
        const sharedFilter = store.filters.get("primary");
        if (me.remoteFilter) {
          store.filterParamName = filterParamName;
        }
        remoteFilter = store.remoteFilter;
        if (sharedFilter) {
          me.primaryFilter = sharedFilter;
        } else {
          storeFilters.push(me.primaryFilter);
        }
      } else {
        if (typeof store === "string") {
          store = Store.getStore(store);
        } else {
          store = new (store.readUrl ? AjaxStore : Store)(store);
          me.destroyStore = true;
        }
        remoteFilter = me.remoteFilter || store.restfulFilter;
        if (remoteFilter && filterParamName) {
          store.filterParamName = filterParamName;
          if (me.encodeFilterParams) {
            store.encodeFilterParams = me.encodeFilterParams;
          }
        }
        storeFilters.push(me.primaryFilter);
      }
      if (!me.valueField && !me.usingRecordAsValue) {
        me.valueField = store.modelClass.idField;
      }
      if (me.filterSelected) {
        const selectedItemsFilter = (r) => !me.containsFocus || !valueCollection.includes(r);
        if (remoteFilter) {
          store.storage.autoFilter = true;
          store.storage.addFilter({
            id: `${me.id}-selected-filter`,
            filterBy: selectedItemsFilter
          });
        } else {
          storeFilters.push(selectedItemsFilter);
          store.reapplyFilterOnAdd = true;
        }
      }
      valueCollection.addIndex({
        property: me.displayField,
        unique: false
      });
      valueCollection.addIndex({
        property: me.valueField,
        unique: true
      });
      storeFilters.forEach((f) => store.addFilter(f, true));
      if (remoteFilter) {
        me.keyStrokeFilterDelay = Math.max(300, keyStrokeFilterDelay || 0);
      } else {
        me.keyStrokeFilterDelay = keyStrokeFilterDelay != null ? keyStrokeFilterDelay : 10;
      }
    }
    if (me.valueField === me.displayField && !me.peekConfig("multiSelect")) {
      me.nullValue = "";
    }
    return store;
  }
  updateStore(store, oldStore) {
    const me = this, { _picker } = me;
    let storeListeners;
    if (me.destroyStore && oldStore) {
      oldStore.destroy();
    }
    if (_picker) {
      _picker.store = store;
    }
    store.storage.addIndex({
      property: me.displayField,
      unique: false
    });
    store.storage.addIndex({
      property: me.valueField,
      unique: true
    });
    storeListeners = {
      filter: "onStoreFilter"
    };
    if (me.displayValueRenderer) {
      (storeListeners || (storeListeners = {})).change = "onComboStoreChange";
    }
    me.detachListeners("store");
    (store == null ? void 0 : store.storage) && me.valueCollection.match(store.storage);
    me.syncInputFieldValue();
    if (storeListeners) {
      storeListeners.name = "store";
      storeListeners.thisObj = me;
      store.ion(storeListeners);
    }
  }
  get remoteFilter() {
    var _a5;
    return Boolean(this.filterParamName || ((_a5 = this._store) == null ? void 0 : _a5.restfulFilter));
  }
  /**
   * Get selected record.
   * @property {Core.data.Model}
   * @readonly
   */
  get record() {
    return this.selected;
  }
  /**
   * Get the selected record(s).
   * @property {Core.data.Model[]}
   * @readonly
   */
  get records() {
    return this.valueCollection.values.slice();
  }
  get selected() {
    const { store } = this;
    return this.multiSelect ? this.valueCollection.values.slice().sort((l, r) => store.indexOf(l) - store.indexOf(r))[0] : this.valueCollection.first;
  }
  //endregion
  //region Value handling
  /**
   * Check if field value is valid
   * @internal
   */
  onEditComplete() {
    const me = this, selectionCount = me.valueCollection.count;
    super.onEditComplete();
    if (selectionCount) {
      me.clearError(errorValidateFilter);
      me.syncInputFieldValue();
    }
    if (me.required && !selectionCount) {
      me.setError(errorFieldRequired);
    }
  }
  //endregion
  //region Events
  /**
   * User clicked trigger icon, toggle list.
   * @private
   */
  onTriggerClick(event) {
    var _a5;
    const me = this, activatePicker = "key" in event;
    if (me.remoteFilter && me.filterOnEnter) {
      return;
    }
    if (me.pickerVisible) {
      me.hidePicker();
    } else if (!me.readOnly && !me.disabled) {
      switch ((_a5 = me.triggerAction) == null ? void 0 : _a5.toLowerCase()) {
        case "all":
          me.doFilter(null, activatePicker);
          break;
        case "last":
          me.doFilter(me.lastQuery, activatePicker);
          break;
        default:
          me.doFilter(me.input.value, activatePicker);
      }
    }
  }
  /**
   * User types into input field in editable combo, show list and filter it.
   * @private
   */
  internalOnInput(event) {
    const me = this;
    if (me.clearWhenInputEmpty && me.input.value === "") {
      me._isUserAction = true;
      me.clear();
      me._isUserAction = false;
    }
    me.syncEmpty();
    me.syncInputWidth();
    me.filterOnInput(event);
    me.trigger("input", { value: me.input.value, event });
  }
  filterOnInput(event) {
    const me = this, { value: value2 } = event.type === "input" ? event.target : me.input, inputLen = value2.length;
    me.inputting = true;
    if (me.inlinePicker || inputLen >= me.minChars && (!me.filterOnEnter || event.key === "Enter")) {
      me.doFilter(value2);
    } else {
      if (me.validateFilter && !me.remoteFilter) {
        me[inputLen ? "setError" : "clearError"](errorValidateFilter);
      }
      me.hidePicker();
    }
    me.inputting = false;
  }
  syncInputWidth() {
    const me = this;
    if (me.usesChipView) {
      const input = me.input, inputPadding = me._inputPadding || (me._inputPadding = DomHelper.getEdgeSize(input, "padding", "lr")), value2 = input.value || "", width = (
        // +'W' to avoid text getting clipped or horizontal scrolling
        DomHelper.measureText(value2 + "W", input, false, me.element) + inputPadding.width
      );
      input.style.flex = `1 1 ${Math.ceil(width)}px`;
    }
  }
  doFilter(queryString, activatePicker) {
    var _a5;
    const me = this, {
      store,
      // Force the lazy config to create picker since the List needs to add its beforeLoad listener
      picker
    } = me, disableFilter = queryString == null || queryString === "";
    me.lastQuery = queryString;
    me.primaryFilter.setConfig({
      value: queryString,
      disabled: disableFilter
    });
    if (me.remoteFilter) {
      store.clear(true);
    }
    const onAfterFilter = () => {
      const { navigator: navigator2, isVisible: isVisible2 } = picker;
      if (store.count) {
        if (!disableFilter && navigator2) {
          navigator2.activeItem = 0;
        }
      } else if (!me.remoteFilter && !disableFilter) {
        if (navigator2) {
          navigator2.activeItem = null;
        }
        if (me.editable && me.validateFilter) {
          me.setError(errorValidateFilter);
        }
      }
      isVisible2 && picker.realign();
    };
    (_a5 = me.filterPromise = store.filter()) == null ? void 0 : _a5.then(() => {
      me.filterPromise = null;
      onAfterFilter();
    });
    if (!me.inlinePicker) {
      if (picker == null ? void 0 : picker.isVisible) {
        if (picker.lastAlignSpec.zone === 0) {
          picker.realign();
        }
      } else {
        me.showPicker(activatePicker);
      }
    }
    if (!me.filterPromise) {
      onAfterFilter();
    }
  }
  onStoreFilter({ source: store }) {
    const me = this, picker = me._picker, dataset = picker == null ? void 0 : picker.element.dataset, { count } = store;
    if (me.remoteFilter) {
      if (count) {
        if (picker) {
          picker.navigator.activeItem = 0;
        }
      } else {
        if (me.validateFilter) {
          me.setError(errorValidateFilter);
        }
      }
      if (me.filterSelected && me.valueCollection.count) {
        store.storage.onFiltersChanged({ action: "splice", oldCount: 1 });
        if (picker) {
          picker.refresh();
        }
      }
    }
    if (dataset) {
      if (me.createOnUnmatched && !count && me.primaryFilter.value != null) {
        dataset.addNewValue = me.L("L{addNewValue}")(me.primaryFilter.value);
      } else {
        dataset == null ? true : delete dataset.addNewValue;
      }
    }
  }
  /**
   * This reacts to our {@link #property-valueCollection} being mutated in any way.
   * The `change`, `select` and `action` events are fired here.
   *
   * This could happen in four ways:
   *
   *  - User selected or deselected an item in the dropdown list.
   *  - `set value` changes the content.
   *  - The {@link #config-multiSelect} Chip view (which uses this in its store) deletes a record.
   *  - The application programmatically mutates the {@link #property-valueCollection}.
   *
   * @private
   */
  onValueCollectionChange({ source: valueCollection }) {
    var _a5, _b2, _c2, _d;
    const me = this, {
      multiSelect,
      _picker,
      nullValue
    } = me, hidePicker = (_a5 = me.hidePickerOnSelect) != null ? _a5 : !multiSelect, record = multiSelect ? valueCollection.values.slice() : valueCollection.first, records = valueCollection.values.slice(), isUserAction = me._isUserAction || (_picker == null ? void 0 : _picker._isUserAction) || hidePicker && me.pickerVisible || false, oldValue = me._value;
    if (hidePicker) {
      me.hidePicker();
    }
    if (multiSelect && me.clearTextOnSelection) {
      me.input.value = "";
    }
    if (!valueCollection.count && me.required) {
      me.setError(errorFieldRequired);
    } else {
      me.clearError(errorFieldRequired);
      me.clearError(errorValidateFilter);
    }
    if (me.validateFilter && record) {
      me.clearError(errorValidateFilter);
    }
    if (me.filterSelected) {
      const navigator2 = _picker == null ? void 0 : _picker.navigator, activeItem = Number((_c2 = (_b2 = navigator2 == null ? void 0 : navigator2.activeItem) == null ? void 0 : _b2.dataset) == null ? void 0 : _c2.index);
      if (me.remoteFilter) {
        me.store.storage.onFiltersChanged({ action: "splice", oldCount: 1 });
        if (me._picker) {
          me._picker.refresh();
        }
      } else {
        me.store.filter();
      }
      if (!isNaN(activeItem)) {
        navigator2.activeItem = Math.min(_picker.store.count, activeItem);
      }
    }
    me._value = nullValue;
    const value2 = me._lastValue = me.cacheCurrentValue(me.value);
    me.syncInputFieldValue();
    me.syncEmpty();
    if (!me.isConfiguring) {
      if (value2 !== oldValue && !me.onConfigChange.$nullFn) {
        me.onConfigChange({ name: "value", value: value2, was: oldValue, config: me.$meta.configs.value });
      }
      me.triggerFieldChange({
        value: value2,
        oldValue,
        userAction: isUserAction,
        valid: me.isValid
      });
      (_d = me.trigger) == null ? void 0 : _d.call(me, "select", { record, records, userAction: isUserAction });
      if (me.defaultAction === "select") {
        me.trigger("action", { value: value2, record, records, userAction: isUserAction });
      }
    }
  }
  // Caching a copy of current value, which can be changed by subclasses (see AssignmentField for reference)
  cacheCurrentValue(v) {
    return this._value = v;
  }
  /**
   * This listens for when a record from the list is selected, but is already part of
   * the selection and so the {@link #property-valueCollection} rejects that as a no-op.
   * At this point, the user will still expect the picker to hide.
   * @param {Object} event The noChange event containing the splice parameters
   * @private
   */
  onValueCollectionNoChange({ toAdd }) {
    if (!this.inlinePicker && !this.multiSelect && toAdd.length && this.pickerVisible) {
      this.picker.hide();
      this.syncInputFieldValue();
    }
  }
  //endregion
  //region Picker
  showPicker() {
    var _a5;
    const me = this, { picker } = me;
    if (me.readOnly || me.inlinePicker) {
      return;
    }
    picker.multiSelect = me.multiSelect;
    super.showPicker(...arguments);
    if (me.overlayAnchor && !picker.align.offset) {
      picker.align.offset = -picker.anchorSize[1];
      picker.realign();
    }
    if (picker.restoreActiveItem) {
      if (me.multiSelect) {
        picker.restoreActiveItem(((_a5 = picker.navigator) == null ? void 0 : _a5.previousActiveItem) || me.selected || 0, true);
      } else {
        picker.restoreActiveItem(me.selected || 0, true);
      }
    }
    me.input.focus();
  }
  /**
   * Creates default picker widget
   *
   * @internal
   */
  changePicker(picker, oldPicker) {
    if (picker) {
      const me = this, pickerWidth = me.pickerWidth || (picker == null ? void 0 : picker.width), config = List.mergeConfigs({
        owner: me,
        store: me.store,
        selected: me.valueCollection,
        multiSelect: me.multiSelect,
        cls: me.listCls,
        displayField: me.displayField,
        forElement: me.pickerAlignElement,
        emptyText: me.emptyText,
        align: {
          matchSize: pickerWidth == null ? "min" : false,
          anchor: me.overlayAnchor,
          target: me.pickerAlignElement,
          // Reasonable minimal height to fit few combo items below the combo.
          // When height is not enough, list will appear on top. That works for windows higher than 280px,
          // worrying about shorter windows sounds overkill.
          // We cannot use relative measures here, each combo list item is ~40px high
          minHeight: me.inlinePicker ? null : Math.min(3, me.store.count) * 40
        },
        [me.listItemTpl ? "itemTpl" : void 0]: me.listItemTpl,
        width: pickerWidth,
        navigator: {
          keyEventTarget: me.input,
          activateOnMouseover: !me.inlinePicker
        }
      }, picker);
      if (me.inlinePicker) {
        Object.assign(config, {
          floating: false,
          align: null,
          maxHeight: null,
          appendTo: me.element
        });
      }
      picker = List.reconfigure(oldPicker, picker ? config : null, me);
      picker.element.classList.add("b-combo-picker");
      picker.ion({
        navigate: "onPickerNavigate",
        thisObj: me
      });
      EventHelper.on({
        element: picker.element,
        pointerdown: (event) => me.onPickerNavigate({ event }),
        thisObj: me
      });
    } else {
      oldPicker == null ? void 0 : oldPicker.destroy();
    }
    return picker;
  }
  onPickerNavigate({ event }) {
    if (event.target.matches("[data-add-new-value]")) {
      this.addNewRecord(this.primaryFilter.value);
    }
  }
  onPickerHide() {
    const me = this;
    super.onPickerHide();
    if (me.multiSelect && me.clearTextOnPickerHide && !me.errors) {
      me.input.value = "";
    }
  }
  //endregion
  async internalOnKeyEvent(keyEvent) {
    var _a5;
    const me = this, value2 = me.input[me.inputValueAttr], inputLen = value2.length, {
      key,
      type
    } = keyEvent, {
      primaryFilter,
      store,
      multiSelect,
      minChars,
      // Use the private property names here because these are lazy configs.
      // We do not want to pull them into existence if they have not yet been created.
      _picker: picker,
      _chipView: chipView
    } = me;
    let skipFilter;
    if (type === "keydown" && (picker == null ? void 0 : picker.isVisible) && picker.navigator) {
      const { activeItem } = picker.navigator;
      if (activeItem && multiSelect && key === me.multiValueSeparator) {
        me.input.value = "";
        picker.onItemClick(activeItem, keyEvent);
        primaryFilter.setConfig({
          value: "",
          disabled: true
        });
        store.filter();
        picker.hide();
        keyEvent.preventDefault();
        return;
      }
      if (!activeItem && me.createOnUnmatched && (multiSelect && key === me.multiValueSeparator || key === "Enter")) {
        keyEvent.preventDefault();
        await me.addNewRecord(value2);
        return;
      }
    }
    if (me.editable) {
      if (chipView && !chipView.selected.count && type === "keydown" && key === "Backspace" && !inputLen) {
        chipView.navigator.activeItem = me.valueCollection.count - 1;
        keyEvent.stopImmediatePropagation();
      }
    } else {
      if (type === "keydown" && key === "Backspace" && ((_a5 = me.nonEditableFilterValue) == null ? void 0 : _a5.length)) {
        me.nonEditableFilterValue = me.nonEditableFilterValue.substring(0, me.nonEditableFilterValue.length - 1);
        me.doFilter(me.nonEditableFilterValue);
        me.lastFilterKeypressTime = keyEvent.timeStamp;
      } else if (type === "keypress" && key.length === 1 && key.match(filterKeyTypes)) {
        if (!me.lastFilterKeypressTime || keyEvent.timeStamp - me.lastFilterKeypressTime > me.nonEditableFilterTimeout) {
          me.nonEditableFilterValue = "";
        }
        me.nonEditableFilterValue = (me.nonEditableFilterValue || "") + key;
        me.doFilter(me.nonEditableFilterValue);
        me.lastFilterKeypressTime = keyEvent.timeStamp;
      }
    }
    if (me.pickerVisible && key === "Enter" && me.primaryFilter.value === me.input.value) {
      skipFilter = true;
    }
    super.internalOnKeyEvent(...arguments);
    if (keyEvent.type === "keydown" && key === "Enter" && !skipFilter && me.remoteFilter) {
      if (inputLen >= minChars) {
        keyEvent.stopPropagation();
        me.filterOnInput.now(keyEvent);
      } else if (inputLen > 0) {
        keyEvent.stopPropagation();
        me.setError(errorTooShort + ` (${minChars})`, false, true);
      }
    }
  }
  async addNewRecord(value2) {
    var _a5, _b2;
    const me = this, {
      store,
      _picker: picker,
      valueCollection,
      multiSelect,
      primaryFilter
    } = me, remoteAutoCommit = store.remoteFilter && store.autoCommit;
    if (me.filterPromise) {
      await me.filterPromise;
    }
    if (!remoteAutoCommit) {
      primaryFilter.setConfig({
        value: "",
        disabled: true
      });
      store.filter();
    }
    me._isUserAction = true;
    const [newRecord] = store.add(me.callback(me.createOnUnmatched, me, [value2, me]));
    if (store.isCommitting) {
      let error;
      try {
        await store.autoCommitTimeoutPromise;
        await store.commitPromise;
      } catch (exception) {
        error = (_b2 = (_a5 = exception.response) == null ? void 0 : _a5.parsedJson) == null ? void 0 : _b2.error;
      }
      if (newRecord.isPhantom) {
        me.clearError();
        me.setError(error || errorRecordNotCommitted, false, true);
        store.remove(newRecord);
        store.removed.remove(newRecord);
      }
    }
    if (remoteAutoCommit) {
      primaryFilter.setConfig({
        value: "",
        disabled: true
      });
      store.filter();
    }
    me.input.value = "";
    if (store.includes(newRecord)) {
      valueCollection.splice(multiSelect ? valueCollection.count : 0, multiSelect ? 0 : valueCollection.count, newRecord);
    }
    picker == null ? void 0 : picker.hide();
    me._isUserAction = false;
  }
  changeCreateOnUnmatched(createOnUnmatched) {
    if (createOnUnmatched === true) {
      createOnUnmatched = this.defaultRecordCreator;
    }
    return createOnUnmatched;
  }
  defaultRecordCreator(value2) {
    return this.store.createRecord({
      [this.displayField]: value2
    });
  }
  updateLocalizeDisplayFields(value2) {
    if (value2) {
      this.localizableProperties.push("store");
    } else {
      ArrayHelper.remove(this.localizableProperties, "store");
    }
  }
  localizeProperty(property) {
    if (property === "store") {
      return this.localizeStore();
    }
    return super.localizeProperty(property);
  }
  localizeStore() {
    var _a5;
    const me = this, { displayField } = me;
    let { localizedDisplayFieldsMap } = me;
    if (!localizedDisplayFieldsMap) {
      me.localizedDisplayFieldsMap = localizedDisplayFieldsMap = /* @__PURE__ */ new Map();
    }
    if (!me.store && me.buildItems) {
      me.items = me.buildItems();
    }
    for (const item of me.items) {
      if (item.id) {
        let localeString = localizedDisplayFieldsMap.get(item.id);
        if (!localeString && ((_a5 = item[displayField]) == null ? void 0 : _a5.startsWith("L{"))) {
          localeString = item[displayField];
          localizedDisplayFieldsMap.set(item.id, localeString);
        }
        if (localeString) {
          item[displayField] = (item.localeClass || me).L(localeString);
        }
      }
    }
    if (me.sortItemsOnLocaleChange) {
      me.store.sort();
    }
    me.syncInputFieldValue();
  }
  // The combo "twin" should use same store instance
  createOverflowTwin(overrides) {
    return super.createOverflowTwin({ store: this.store, ...overrides });
  }
};
//region Config
__publicField(Combo, "$name", "Combo");
__publicField(Combo, "type", "combo");
__publicField(Combo, "alias", "combobox,dropdown");
__publicField(Combo, "delayable", {
  filterOnInput: 0
});
var ComboChipView = class extends ChipView {
  static get defaultConfig() {
    return {
      closeHandler: "up.onChipClose",
      itemsFocusable: false,
      multiSelect: true,
      itemTpl(record) {
        return StringHelper.encodeHtml(record.getValue(this.owner.displayField));
      },
      scrollable: {
        overflowY: "auto"
      }
    };
  }
};
__publicField(ComboChipView, "$name", "ComboChipView");
__publicField(ComboChipView, "type", "combochipview");
var ComboChipNavigator = class extends Navigator {
  static get configurable() {
    return {
      allowShiftKey: true
    };
  }
  onTargetClick(clickEvent) {
    const me = this, item = clickEvent.target.closest(me.itemSelector);
    if (item && !clickEvent.target.classList.contains("b-close-icon")) {
      if (!clickEvent.shiftKey && !item.contains(clickEvent.target.closest("[data-noselect]"))) {
        me.ownerCmp.selected.clear();
      }
      me.inClickHandler = true;
      me.activeItem = item;
      me.inClickHandler = false;
    }
  }
  onKeyDown(keyEvent) {
    if (keyEvent.key !== "Enter" && !keyEvent.target.selectionStart && !keyEvent.target.selectionEnd) {
      super.onKeyDown(keyEvent);
    }
  }
  updateActiveItem(activeItem, oldActiveItem) {
    const chipView = this.ownerCmp;
    super.updateActiveItem(activeItem, oldActiveItem);
    if (activeItem && !this.inClickHandler) {
      chipView.selected.add(chipView.getRecordFromElement(activeItem));
    }
  }
  navigatePrevious(keyEvent) {
    const chipView = this.ownerCmp;
    if (chipView.navigator.activeItem && !keyEvent.shiftKey) {
      chipView.selected.clear();
    }
    if (this.previous) {
      super.navigatePrevious(keyEvent);
    } else {
      this.activeItem = null;
    }
  }
  navigateNext(keyEvent) {
    const chipView = this.ownerCmp;
    if (chipView.navigator.activeItem && !keyEvent.shiftKey) {
      chipView.selected.clear();
    }
    if (this.next) {
      super.navigateNext(keyEvent);
    } else {
      this.activeItem = null;
    }
  }
};
__publicField(ComboChipNavigator, "$name", "ComboChipNavigator");
// Factoryable type name
__publicField(ComboChipNavigator, "type", "combochipnavigator");
Combo.initClass();
ComboChipView.initClass();
ComboChipNavigator.initClass();
Combo._$name = "Combo";

// ../Core/lib/Core/widget/BooleanCombo.js
var BooleanCombo = class extends Combo {
  //region Config
  static get configurable() {
    return {
      /**
       * Positive option value
       *
       * @config {*}
       */
      positiveValue: true,
      /**
       * Positive option display value
       *
       * @config {String}
       */
      positiveText: null,
      /**
       * Negative option value
       *
       * @config {*}
       */
      negativeValue: false,
      /**
       * False option display value
       *
       * @config {String}
       */
      negativeText: null,
      store: {
        value: [],
        $config: "lazy"
      },
      /**
       * Default value
       *
       * @config {*}
       */
      value: false
    };
  }
  //endregion
  changeStore(store, oldStore) {
    const me = this;
    return super.changeStore(new Store({
      data: [{
        id: me.positiveValue,
        text: me.positiveText || me.L("L{Object.Yes}")
      }, {
        id: me.negativeValue,
        text: me.negativeText || me.L("L{Object.No}")
      }]
    }), oldStore);
  }
};
__publicField(BooleanCombo, "$name", "BooleanCombo");
__publicField(BooleanCombo, "type", "booleancombo");
BooleanCombo.initClass();
BooleanCombo._$name = "BooleanCombo";

// ../Core/lib/Core/widget/ButtonGroup.js
var toggleOrderFn = ({ $toggleOrder: a }, { $toggleOrder: b }) => (b || 0) - (a || 0);
var buttonListeners = {
  click: "resetValueCache",
  beforeToggle: "onItemBeforeToggle",
  toggle: "onItemToggle"
};
var _ButtonGroup = class _ButtonGroup extends Container.mixin(Rotatable_default, Validatable_default) {
  afterConstruct() {
    super.afterConstruct();
    this.ion({
      change: "internalOnChange"
    });
  }
  // ButtonGroup has no wrapper like field does
  // so return element instead since error tooltip uses field.inputWrap
  get inputWrap() {
    return this.element;
  }
  get isValid() {
    let result = super.isValid;
    const me = this, { toggleable } = me;
    me.clearError("L{Field.fieldRequired}", true);
    if (result && !me.disabled) {
      if (me.required && !me.value || toggleable && me.pressed.length < toggleable[0]) {
        result = false;
        me.setError("L{Field.fieldRequired}", true);
      }
    }
    if (me.checkValidity && me.callback(me.checkValidity, me, [me]) === false) {
      result = false;
    }
    if (result && me.errors && Object.keys(me.errors).length) {
      result = false;
    }
    return result;
  }
  get pressed() {
    return this.items.filter((b) => b.pressed).sort(toggleOrderFn);
  }
  internalOnChange() {
    this.syncInvalid();
  }
  onChildAdd(item) {
    super.onChildAdd(item);
    item.ion({
      thisObj: this,
      ...buttonListeners,
      // This needs to run before the 'click' event is relayed by this button group, in such listener
      // the `value` must already be updated
      prio: 1e4
    });
  }
  onChildRemove(item) {
    item.un({
      thisObj: this,
      ...buttonListeners
    });
    super.onChildRemove(item);
  }
  onItemBeforeToggle({ pressed }) {
    const { toggleable } = this;
    return pressed || !toggleable || this.pressed.length > toggleable[0];
  }
  onItemToggle(event) {
    const me = this, toggleCount = (me.toggleCount || 0) + 1, { toggleable } = me;
    me.resetValueCache();
    if (!me.isSettingValue && (!me.toggleGroup || event.pressed)) {
      if (toggleable) {
        event.source.$toggleOrder = toggleCount;
        me.toggleCount = toggleCount;
        me.isSettingValue = true;
        me.pressed.slice(toggleable[1]).forEach((b) => b.pressed = false);
        me.isSettingValue = false;
      }
      me.triggerFieldChange({ value: me.value, userAction: true, event });
    }
  }
  resetValueCache() {
    this._value = null;
  }
  createWidget(widget) {
    const me = this, type = me.constructor.resolveType(widget.type || "button");
    if (type.isButton) {
      if (me.color && !widget.color) {
        widget.color = me.color;
      }
      if (!widget.toggleGroup && me.toggleGroup) {
        widget.toggleGroup = me.toggleGroup;
      }
      if (me.toggleable) {
        widget.toggleable = true;
      }
    }
    if (me.columns) {
      widget.width = `${100 / me.columns}%`;
    }
    widget = super.createWidget(widget);
    me.relayEvents(widget, ["click", "action", "toggle"]);
    return widget;
  }
  changeToggleable(toggleable) {
    if (toggleable === true) {
      toggleable = [0, Infinity];
    } else if (typeof toggleable === "number") {
      toggleable = [1, toggleable];
    }
    return toggleable || null;
  }
  updateToggleable(toggleable) {
    if (toggleable && !this.isConstructing) {
      this.pressed.slice(toggleable[1]).forEach((b) => b.pressed = false);
    }
  }
  changeToggleGroup(toggleGroup, was) {
    return toggleGroup === true ? was || _ButtonGroup.generateId("toggleGroup") : toggleGroup;
  }
  updateRotate(rotate) {
    this.eachWidget((btn) => {
      if (btn.rotate !== false) {
        btn.rotate = rotate;
      }
    });
  }
  get valueAsArray() {
    const values = [];
    this.items.forEach((w) => {
      if (w.pressed && w.value !== void 0) {
        values.push(w.value);
      }
    });
    return values;
  }
  get value() {
    const me = this;
    if (!me._value) {
      me._value = me.valueAsArray.join(me.valueSeparator);
    }
    return me._value;
  }
  set value(value2) {
    const me = this, oldValue = me.value;
    if (value2 == null) {
      value2 = [];
    } else if (typeof value2 == "string") {
      value2 = value2.split(me.valueSeparator);
    } else if (!Array.isArray(value2)) {
      value2 = [value2];
    }
    me._value = value2.join(me.valueSeparator);
    me.isSettingValue = true;
    me.items.forEach((w) => {
      if (w.value !== void 0) {
        w.pressed = value2.includes(w.value);
      }
    });
    me.isSettingValue = false;
    if (!me.isConfiguring && oldValue !== me.value) {
      me.triggerFieldChange({ value: me.value, userAction: Boolean(me._isUserAction) });
    }
  }
  get validity() {
    return {};
  }
  onDisabled(disabled) {
    this.syncInvalid();
    this.items.forEach((button) => button.disabled = disabled || !button.ignoreParentReadOnly && this.readOnly);
  }
  updateReadOnly(readOnly) {
    super.updateReadOnly(readOnly);
    this.updateDisabled(this.disabled);
  }
  addToOverflowMenu(menu, overrides, onCreateTwin2) {
    const { toggleable, toggleGroup } = this;
    if (toggleGroup || (toggleable == null ? void 0 : toggleable[0]) === 1 && toggleable[1] === 1) {
      overrides = {
        ...overrides,
        radio: true
      };
    }
    this.items.forEach((it) => it.addToOverflowMenu(menu, overrides, onCreateTwin2));
  }
  get widgetClassList() {
    const classList = super.widgetClassList;
    this.columns && classList.push("b-columned");
    return classList;
  }
  updateUseGap(useGap) {
    if (!useGap) {
      this.element.classList.add("b-no-gap");
    }
  }
};
__publicField(_ButtonGroup, "$name", "ButtonGroup");
__publicField(_ButtonGroup, "type", "buttongroup");
/**
 * Fires when a button in the group is clicked
 * @event click
 * @param {Core.widget.Button} source Clicked button
 * @param {Event} event DOM event
 */
/**
 * Fires when the default action is performed on a button in the group (the button is clicked)
 * @event action
 * @param {Core.widget.Button} source Clicked button
 * @param {Event} event DOM event
 */
/**
 * Fires when a button in the group is toggled (the {@link Core.widget.Button#property-pressed} state is changed).
 * If you need to process the pressed button only, consider using {@link #event-click} event or {@link #event-action} event.
 * @event toggle
 * @param {Core.widget.Button} source Toggled button
 * @param {Boolean} pressed New pressed state
 * @param {Event} event DOM event
 */
__publicField(_ButtonGroup, "configurable", {
  defaultType: "button",
  /**
   * Custom CSS class to add to element. When using raised buttons (cls 'b-raised' on the buttons), the group
   * will look nicer if you also set that cls on the group.
   *
   * ```
   * new ButtonGroup({
   *   cls : 'b-raised,
   *   items : [
   *       { icon : 'b-fa b-fa-unicorn', cls : 'b-raised' },
   *       ...
   *   ]
   * });
   * ```
   *
   * @config {String}
   * @category CSS
   */
  cls: null,
  /**
   * An array of Buttons or typed Button config objects.
   * @config {ButtonConfig[]|Core.widget.Button[]}
   */
  items: null,
  /**
   * Default color to apply to all contained buttons, see {@link Core.widget.Button#config-color Button#color}.
   * Individual buttons can override the default.
   * @config {String}
   */
  color: null,
  /**
   * If you use gaps between your buttons, set this to `true` (controls whether to use border-radius for the button
   * elements)
   * @config {Boolean}
   * @default
   */
  useGap: false,
  /**
   * Set to `true` to turn the ButtonGroup into a toggle group, assigning a generated value to each contained
   * buttons {@link Core.widget.Button#config-toggleGroup toggleGroup config}. Individual buttons can
   * override the default.
   * @config {Boolean}
   */
  toggleGroup: null,
  valueSeparator: ",",
  columns: null,
  hideWhenEmpty: true,
  defaultBindProperty: "value",
  errorTooltipCachePath: "buttonGroupErrorTooltip",
  errorTooltipConfig: {
    forSelector: ".b-buttongroup.b-invalid"
  },
  overflowable: true,
  /**
   * A two-element array containing the minimum and maximum number of toggleable buttons. If a single number is
   * specified, it is converted to an array using that given value as the maximum and 1 is used as the minimum.
   * If `true` is specified, it is equivalent to `[0, Infinity]`, allowing any buttons to be toggled independently
   * or all buttons to be untoggled.
   *
   * A value of `null` (the default) is similar to `toggleable : 1` unless {@link #config-toggleGroup} is set,
   * or individual buttons are configured with their own {@link Core.widget.Button#config-toggleable} or
   * {@link Core.widget.Button#config-toggleGroup} values. If this config is used, `toggleable` and `toggleGroup`
   * should not be assigned to individual buttons.
   *
   * @prp {Number[]}
   * @accepts {Boolean|Number}
   */
  toggleable: null
});
var ButtonGroup = _ButtonGroup;
ButtonGroup.initClass();
ButtonGroup._$name = "ButtonGroup";

// ../Core/lib/Core/widget/CalendarPanel.js
var weekendDays = { 0: 1, 6: 1 };
var CalendarPanel = class extends Panel {
  static get configurable() {
    return {
      layout: "vbox",
      textContent: false,
      /**
       * Gets or sets the date that orientates the panel to display a particular month.
       * Changing this causes the content to be refreshed.
       * @member {Date} date
       */
      /**
       * The date which this CalendarPanel encapsulates.
       * @config {Date|String}
       */
      date: {
        $config: "date"
      },
      /**
       * A {@link Core.util.Month} Month utility object which encapsulates this Panel's month
       * and provides contextual information and navigation services.
       * @config {Core.util.Month|MonthConfig}
       */
      month: {},
      year: null,
      /**
       * The week start day, 0 meaning Sunday, 6 meaning Saturday.
       * Defaults to {@link Core.helper.DateHelper#property-weekStartDay-static}.
       * @config {Number}
       */
      weekStartDay: null,
      /**
       * Configure as `true` to always show a six-week calendar.
       * @config {Boolean}
       * @default
       */
      sixWeeks: true,
      /**
       * Configure as `true` to show a week number column at the start of the calendar block.
       * @config {Boolean}
       */
      showWeekColumn: null,
      /**
       * Either an array of `Date` objects which are to be disabled (or date strings), or
       * a function (or the name of a function), which, when passed a `Date` returns `true` if the
       * date is disabled.
       * @config {Function|String|Date[]|String[]}
       * @param {Date} date Date to check
       * @returns {Boolean} Returns `true` if the provided date is disabled
       */
      disabledDates: null,
      /**
       * A function (or the name of a function) which creates content in, and may mutate a day header element.
       *
       * The cell element may be mutated, or an HTML string or {@link DomConfig} object may be returned.
       * @config {Function|String}
       * @param {HTMLElement} cell The header element
       * @param {Number} columnIndex The day column. Will be in the range `0` to `6`
       * @param {Number} weekDay The canonical day number where Monday is `0` and Sunday is `6`
       * @returns {String|DomConfig|null}
       */
      headerRenderer: null,
      /**
       * A function (or the name of a function) which creates content in, and may mutate the week cell element at the start of a week row.
       *
       * ```javascript
       * weekRenderer(wekkCell, week) {
       *     // week[0] is the year
       *     // week[1] is the week number
       *     cell.innerText = week[1];
       * }
       * ```
       *
       * The cell element may be mutated, or an HTML string or {@link DomConfig} object may be returned.
       * @config {Function|String}
       * @param {HTMLElement} weekCell The header element
       * @param {Number[]} week An array containing `[year, weekNumber]`
       * @returns {String|DomConfig|null}
       */
      weekRenderer: null,
      /**
       * A function (or the name of a function) which creates content in, and may mutate a day cell element.
       *
       * @config {Function|String}
       * @param {Object} renderData
       * @param {HTMLElement} renderData.cell The header element
       * @param {Date} renderData.date The date for the cell
       * @param {Number} renderData.day The day for the cell (`0` to `6` for Sunday to Saturday)
       * @param {Number[]} renderData.rowIndex The row index, 0 to month row count (6 if {@link #config-sixWeeks} is `true`)
       * @param {HTMLElement} renderData.row The row element encapsulating the week which the cell is a part of
       * @param {Core.widget.CalendarPanel} renderData.source The widget being rendered
       * @param {Number[]} renderData.cellIndex The cell index in the whole panel. May be from `0` to up to `42`
       * @param {Number[]} renderData.columnIndex The column index, `0` to `6`
       * @param {Number[]} renderData.visibleColumnIndex The visible column index taking hidden non working days into account
       * @returns {String|DomConfig|null}
       */
      cellRenderer: null,
      /**
       * Configure as `true` to render Saturdays and Sundays as {@link #config-disabledDates}.
       * @config {Boolean}
       */
      disableWeekends: null,
      /**
       * Configure as `true` to render non working days as {@link #config-disabledDates}.
       *
       * Note, that by default, non working days are read from the locale, and are normally
       * Saturday and Sunday. But they can be set to other days than weekend days.
       * @config {Boolean}
       */
      disableNonWorkingDays: null,
      hideNonWorkingDays: null,
      hideNonWorkingDaysCls: "b-hide-nonworking-days",
      /**
       * Non-working days as an object where keys are day indices, 0-6 (Sunday-Saturday), and the value is `true`.
       * Defaults to {@link Core.helper.DateHelper#property-nonWorkingDays-static}.
       * @config {Object<Number,Boolean>}
       */
      nonWorkingDays: null,
      /**
       * A config object to create a tooltip which will show on hover of a date cell including disabled, weekend,
       * and "other month" cells.
       *
       * It is the developer's responsibility to hook the `beforeshow` event to either veto the show by returning
       * `false` or provide contextual content for the date.
       *
       * The tip instance will be primed with a `date` property.
       * @config {TooltipConfig}
       */
      tip: null,
      dayCellCls: "b-calendar-cell",
      dayHeaderCls: "b-calendar-day-header",
      /**
       * The class name to add to disabled calendar cells.
       * @config {String}
       */
      disabledCls: "b-disabled-date",
      /**
       * The class name to add to calendar cells which are in the previous or next month.
       * @config {String}
       */
      otherMonthCls: "b-other-month",
      /**
       * The class name to add to calendar cells which are weekend dates.
       * @config {String}
       */
      weekendCls: "b-weekend",
      /**
       * The class name to add to the calendar cell which contains today's date.
       * @config {String}
       */
      todayCls: "b-today",
      /**
       * The class name to add to calendar cells which are {@link #config-nonWorkingDays}.
       * @config {String}
       */
      nonWorkingDayCls: "b-nonworking-day",
      /**
       * The {@link Core.helper.DateHelper} format string to format the day names
       * in the header row above the calendar cells.
       * @config {String}
       * @default
       */
      dayNameFormat: "ddd",
      /**
       * By default, week rows flex to share available Panel height equally.
       *
       * Set this config if the available height is too small, and the cell height needs
       * to be larger to show events.
       *
       * Setting this config causes the month grid to become scrollable in the `Y` axis.
       *
       * May be specified as a number in which case it will be taken to mean pixels,
       * or a length in standard CSS units.
       * @config {Number|String}
       */
      minRowHeight: {
        $config: ["lazy"],
        value: null
      },
      /**
       * By default, day cells flex to share available Panel width equally.
       *
       * Set this config if the available width is too small, and the cell width needs
       * to be larger to show events.
       *
       * Setting this config causes the month grid to become scrollable in the `X` axis.
       * @config {Number}
       */
      minColumnWidth: {
        $config: ["lazy"],
        value: null
      },
      /**
       * Configure this as true to disable pointer interaction with cells which are outside the
       * range of the current month.
       * @config {Boolean}
       */
      disableOtherMonthCells: null,
      disableOtherMonthCellsCls: "b-disable-othermonth-cells",
      /**
       * Configure this as `true` to hide cells which are outside the range of the current month.
       * @config {Boolean}
       */
      hideOtherMonthCells: null,
      hideOtherMonthCellsCls: "b-hide-othermonth-cells",
      /**
       * By default, when navigating through time, the next time
       * block will be animated in from the appropriate direction.
       *
       * Configure this as `false` to disable this.
       * @prp {Boolean} animateTimeShift
       * @default
       */
      animateTimeShift: true
    };
  }
  construct(config) {
    super.construct(config);
    if (!this.refreshCount) {
      this.refresh();
    }
  }
  onInternalPaint({ firstPaint }) {
    var _a5;
    (_a5 = super.onInternalPaint) == null ? void 0 : _a5.call(this, ...arguments);
    if (firstPaint) {
      if (!this.refreshCount) {
        this.refresh();
      }
      this.getConfig("minColumnWidth");
      this.getConfig("minRowHeight");
    }
  }
  get overflowElement() {
    return this.weeksElement;
  }
  doDestroy() {
    var _a5;
    (_a5 = this.tip) == null ? void 0 : _a5.destroy();
    super.doDestroy();
  }
  updateMinRowHeight(minRowHeight) {
    this.element.style.setProperty("--min-row-height", DomHelper.setLength(minRowHeight));
    this.scrollable = {
      overflowY: minRowHeight ? "auto" : false
    };
  }
  updateMinColumnWidth(minColumnWidth) {
    this.element.style.setProperty("--min-column-width", DomHelper.setLength(minColumnWidth));
    this.scrollable = {
      overflowX: minColumnWidth ? "auto" : false
    };
    this.overflowElement.classList[minColumnWidth ? "add" : "remove"]("b-min-columnwidth");
  }
  getDateFromDomEvent(domEvent) {
    const element = (domEvent.nodeType === Element.ELEMENT_NODE ? domEvent : domEvent.target).closest(`#${this.id} [data-date]`);
    if (element) {
      return DateHelper.parseKey(element.dataset.date);
    }
  }
  changeTip(tip, existingTip) {
    const me = this;
    return Tooltip.reconfigure(existingTip, tip, {
      owner: me,
      defaults: {
        type: "tooltip",
        owner: me,
        id: `${me.id}-cell-tip`,
        forElement: me.bodyElement,
        forSelector: `.${me.dayCellCls}`
      }
    });
  }
  updateTip(tip) {
    this.detachListeners("tip");
    tip == null ? void 0 : tip.ion({
      pointerOver: "onTipOverCell",
      name: "tip",
      thisObj: this
    });
  }
  updateElement(element, was) {
    const me = this;
    super.updateElement(element, was);
    me.updateHideNonWorkingDays(me.hideNonWorkingDays);
    me.weekdayCells = Array.from(element.querySelectorAll(".b-calendar-day-header"));
    me.weekElements = Array.from(element.querySelectorAll(".b-calendar-week"));
    me.weekDayElements = Array.from(element.querySelectorAll(".b-calendar-days"));
    me.cellElements = [];
    for (let i = 0, { length } = me.weekDayElements; i < length; i++) {
      me.cellElements.push(me.weekDayElements[i].previousSibling, ...me.weekDayElements[i].children);
    }
  }
  changeDate(date2) {
    date2 = typeof date2 === "string" ? DateHelper.parse(date2) : new Date(date2);
    if (isNaN(date2)) {
      throw new Error("CalendarPanel date ingestion must be passed a Date, or a YYYY-MM-DD date string");
    }
    return DateHelper.clearTime(date2);
  }
  /**
   * The date which this CalendarPanel encapsulates. Setting this causes the
   * content to be refreshed.
   * @property {Date}
   */
  updateDate(value2) {
    this.month.date = value2;
    this.element.dataset.month = DateHelper.format(value2, "YYYY-MM");
  }
  updateDayNameFormat() {
    const d = /* @__PURE__ */ new Date("2000-06-04T12:00:00");
    this.shortDayNames = [];
    for (let date2 = 4; date2 < 11; date2++) {
      d.setDate(date2);
      this.shortDayNames.push(DateHelper.format(d, this.dayNameFormat));
    }
  }
  get weekStartDay() {
    return typeof this._weekStartDay === "number" ? this._weekStartDay : DateHelper.weekStartDay;
  }
  /**
   * Set to 0 for Sunday (the default), 1 for Monday etc.
   *
   * Set to `null` to use the default value from {@link Core/helper/DateHelper}.
   */
  updateWeekStartDay(weekStartDay) {
    const me = this;
    if (me._month) {
      me.month.weekStartDay = weekStartDay;
      me.dayNames = [];
      for (let i = 0; i < 7; i++) {
        me.dayNames[i] = me.shortDayNames[me.canonicalDayNumbers[i]];
      }
      if (me.refreshCount) {
        me.refresh();
      }
    }
  }
  updateHideNonWorkingDays(hideNonWorkingDays) {
    var _a5;
    this.contentElement.classList.toggle(this.hideNonWorkingDaysCls, Boolean(hideNonWorkingDays));
    (_a5 = this.scrollable) == null ? void 0 : _a5.syncOverflowState();
    if (this._month) {
      this.month.hideNonWorkingDays = hideNonWorkingDays;
    }
    if (!this.isConfiguring) {
      this.refresh();
    }
  }
  updateHideOtherMonthCells(hideOtherMonthCells) {
    var _a5;
    this.element.classList.toggle(this.hideOtherMonthCellsCls, Boolean(hideOtherMonthCells));
    (_a5 = this.scrollable) == null ? void 0 : _a5.syncOverflowState();
  }
  updateDisableOtherMonthCells(disableOtherMonthCells) {
    var _a5;
    this.element.classList.toggle(this.disableOtherMonthCellsCls, Boolean(disableOtherMonthCells));
    (_a5 = this.scrollable) == null ? void 0 : _a5.syncOverflowState();
  }
  get nonWorkingDays() {
    return this._nonWorkingDays || (this._localeNonWorkingDays || (this._localeNonWorkingDays = DateHelper.nonWorkingDays));
  }
  get weekends() {
    return this._localeWeekends || (this._localeWeekends = DateHelper.weekends);
  }
  changeNonWorkingDays(nonWorkingDays) {
    return ObjectHelper.assign({}, nonWorkingDays);
  }
  updateNonWorkingDays(nonWorkingDays) {
    var _a5;
    if (this._month) {
      this.month.nonWorkingDays = nonWorkingDays;
      this.refresh();
      (_a5 = this.scrollable) == null ? void 0 : _a5.syncOverflowState();
    }
  }
  get visibleDayColumnIndex() {
    return this.month.visibleDayColumnIndex;
  }
  get dayColumnIndex() {
    return this.month.dayColumnIndex;
  }
  get canonicalDayNumbers() {
    return this.month.canonicalDayNumbers;
  }
  get visibleColumnCount() {
    return this.month.visibleColumnCount;
  }
  get weekLength() {
    return this.month.weekLength;
  }
  /**
   * The date of the first day cell in this panel.
   * Note that this may *not* be the first of this panel's current month.
   * @property {Date}
   * @readonly
   */
  get startDate() {
    return this.month.startDate;
  }
  get duration() {
    return DateHelper.diff(this.month.startDate, this.month.endDate, "day") + 1;
  }
  /**
   * The end date of this view. Note that in terms of full days, this is exclusive,
   * ie: 2020-01-012 to 2020-01-08 is *seven* days. The end is 00:00:00 on the 8th.
   *
   * Note that this may *not* be the last date of this panel's current month.
   * @property {Date}
   * @readonly
   */
  get endDate() {
    const { endDate } = this.month;
    if (endDate) {
      return DateHelper.add(endDate, 1, "day");
    }
  }
  changeMonth(month2, currentMonth) {
    const me = this;
    if (!(month2 instanceof Month)) {
      if (typeof month2 === "number") {
        if (currentMonth) {
          currentMonth.month = month2;
          return;
        }
        const date2 = me.date || DateHelper.clearTime(/* @__PURE__ */ new Date());
        date2.setMonth(month2);
        month2 = {
          date: date2
        };
      }
      month2 = Month.new({
        weekStartDay: me.weekStartDay,
        nonWorkingDays: me.nonWorkingDays,
        hideNonWorkingDays: me.hideNonWorkingDays,
        sixWeeks: me.sixWeeks
      }, month2);
    }
    month2.ion({
      dateChange: "onMonthDateChange",
      thisObj: me
    });
    return month2;
  }
  onMonthDateChange({ source: month2, newDate, oldDate, changes }) {
    const me = this;
    me.year = month2.year;
    if (!me.isConfiguring) {
      if (!me.getCell(newDate) || (changes.m || changes.y)) {
        const { isVisible: isVisible2 } = me;
        me.refresh();
        if (me.animateTimeShift && isVisible2) {
          DomHelper.slideIn(me.contentElement, newDate > oldDate ? 1 : -1);
        }
      }
      me.trigger("dateChange", {
        changes,
        value: newDate,
        oldValue: oldDate
      });
    }
  }
  updateYear(year) {
    this.month.year = year;
  }
  updateShowWeekNumber(showWeekNumber) {
    this.updateShowWeekColumn(showWeekNumber);
  }
  updateShowWeekColumn(showWeekColumn) {
    const me = this;
    me.element.classList[showWeekColumn ? "add" : "remove"]("b-show-week-column");
    if (me.floating) {
      if (!me.isAligning) {
        me.realign();
      }
    }
  }
  updateSixWeeks(sixWeeks) {
    if (this.month) {
      this.month.sixWeeks = sixWeeks;
      this.refresh();
    }
  }
  /**
   * Refreshes the UI after changing a config that would affect the UI.
   */
  refresh() {
    this.month && this.element.style.setProperty("--week-length", this.month.weekLength);
    this.doRefresh();
  }
  /**
   * Implementation of the UI refresh.
   * @private
   */
  doRefresh() {
    var _a5;
    this.getConfig("element");
    const me = this, timeZone = me.timeZone != null ? me.timeZone : (_a5 = me.project) == null ? void 0 : _a5.timeZone, today = timeZone != null ? TimeZoneHelper.toTimeZone(/* @__PURE__ */ new Date(), timeZone) : /* @__PURE__ */ new Date(), {
      weekElements,
      weekDayElements,
      date: date2,
      month: month2,
      dayCellCls,
      dayHeaderCls,
      disabledCls,
      otherMonthCls,
      weekendCls,
      todayCls,
      nonWorkingDayCls,
      nonWorkingDays,
      canonicalDayNumbers,
      sixWeeks
    } = me;
    today.setHours(0, 0, 0, 0);
    if (!date2) {
      me.date = today;
      return;
    }
    me.element.style.setProperty("--visible-week-count", month2.weekCount);
    me.trigger("beforeRefresh");
    me.getConfig("dayNameFormat");
    for (let columnIndex = 0; columnIndex < 7; columnIndex++) {
      const cell = me.weekdayCells[columnIndex], cellDay = me.canonicalDayNumbers[columnIndex], cellClassList = {
        [dayHeaderCls]: 1,
        [weekendCls]: weekendDays[cellDay],
        [nonWorkingDayCls]: nonWorkingDays[cellDay]
      };
      if (me.headerRenderer) {
        cell.innerHTML = "";
        const header = me.callback(me.headerRenderer, me, [cell, columnIndex, cellDay]);
        if (header) {
          if (typeof header === "string") {
            DomHelper.setInnerText(cell, header);
          } else {
            DomHelper.createElement({
              ...header,
              parent: cell
            });
          }
        }
      } else {
        DomHelper.setInnerText(cell, me.shortDayNames[cellDay]);
      }
      DomHelper.syncClassList(cell, cellClassList);
      cell.dataset.columnIndex = columnIndex;
      cell.dataset.cellDay = cellDay;
    }
    let rowIndex = 0, cellIndex = 0, lastWorkingColumn = 6;
    for (let columnIndex = 6; columnIndex >= 0; columnIndex--) {
      if (!nonWorkingDays[canonicalDayNumbers[columnIndex]]) {
        lastWorkingColumn = columnIndex;
        break;
      }
    }
    weekElements[4].classList.toggle("b-hide-display", month2.weekCount < 5 && !sixWeeks);
    weekElements[5].classList.toggle("b-hide-display", month2.weekCount < 6 && !sixWeeks);
    month2.eachWeek((week, dates) => {
      const weekDayElement = weekDayElements[rowIndex], weekCells = [weekDayElement.previousSibling, ...weekDayElement.children], weekCell = weekCells[0];
      weekElements[rowIndex].dataset.week = `${week[0]},${week[1]}`;
      weekElements[rowIndex].setAttribute("aria-label", `${me.L("L{Week}")} ${week[1]}, ${week[0]}`);
      if (weekCell.innerHTML === "") {
        weekCell.innerText = week[1];
      }
      if (me.weekRenderer) {
        const weekCellContent = me.callback(me.weekRenderer, me, [weekCell, week]);
        if (weekCellContent) {
          if (typeof weekCellContent === "string") {
            DomHelper.setInnerText(weekCell, weekCellContent);
          } else {
            weekCell.innerHTML = "";
            DomHelper.createElement({
              ...weekCellContent,
              parent: weekCell
            });
          }
        }
      } else {
        weekCell.innerText = week[1];
      }
      for (let columnIndex = 0; columnIndex < 7; columnIndex++) {
        const date3 = dates[columnIndex], day2 = date3.getDay(), key = DateHelper.makeKey(date3), isNonWorking = nonWorkingDays[day2], cell = weekCells[columnIndex + 1], isToday = DateHelper.isToday(date3, me.timeZone), cellClassList = {
          [dayCellCls]: 1,
          [disabledCls]: me.isDisabledDate(date3),
          [otherMonthCls]: date3.getMonth() !== month2.month,
          [weekendCls]: weekendDays[day2],
          [todayCls]: isToday,
          [nonWorkingDayCls]: isNonWorking,
          "b-last-working-day": columnIndex === lastWorkingColumn,
          "b-first-visible-cell": !(date3 - (me.firstVisibleDate || -1)),
          "b-last-visible-cell": !(date3 - (me.lastVisibleDate || -1)),
          [`b-day-of-week-${day2}`]: 1
        };
        DomHelper.syncClassList(cell, cellClassList);
        cell.role = "gridcell";
        cell.dataset.date = key;
        cell.dataset.cellIndex = cellIndex;
        cell.dataset.columnIndex = columnIndex;
        if (cell.lastDomConfig) {
          delete cell.lastDomConfig.class;
          delete cell.lastDomConfig.className;
        }
        if (me.cellRenderer) {
          me.callback(me.cellRenderer, me, [{
            cell,
            date: date3,
            day: day2,
            rowIndex,
            cellIndex,
            columnIndex,
            key,
            week,
            row: weekElements[rowIndex],
            source: me,
            visibleColumnIndex: me.visibleDayColumnIndex[day2]
          }]);
          if (isToday) {
            const ariaLabel = cell.getAttribute("aria-label"), todayText = me.L("L{Object.today}, ");
            if (ariaLabel && !ariaLabel.startsWith(todayText)) {
              cell.setAttribute("aria-label", `${todayText}${ariaLabel}`);
            }
          }
        } else {
          cell.innerHTML = date3.getDate();
        }
        cellIndex++;
      }
      rowIndex++;
    });
    me.visibleWeekCount = rowIndex;
    if (me.floating) {
      if (!me.isAligning) {
        me.realign();
      }
    }
    me.refreshCount = (me.refreshCount || 0) + 1;
    me.trigger("refresh");
  }
  isDisabledDate(date2) {
    const me = this, day2 = date2.getDay(), {
      disabledDates
    } = me;
    if (me.disableWeekends && weekendDays[day2]) {
      return true;
    }
    if (me.disableNonWorkingDays && me.nonWorkingDays[day2]) {
      return true;
    }
    if (disabledDates) {
      if (Array.isArray(disabledDates)) {
        date2 = DateHelper.clearTime(date2, true);
        return disabledDates.some((d) => {
          d = typeof d === "string" ? DateHelper.parse(d) : d;
          return !(DateHelper.clearTime(d, true) - date2);
        });
      } else {
        return me.callback(disabledDates, me, [date2]);
      }
    }
  }
  get bodyConfig() {
    const result = super.bodyConfig, weeksContainerChildren = [];
    result.children = [{
      tag: "div",
      className: "b-calendar-row b-calendar-weekdays",
      "aria-hidden": true,
      reference: "weekdaysHeader",
      children: [
        { class: "b-week-number-cell" },
        ...ArrayHelper.fill(7, { class: this.dayHeaderCls }),
        DomHelper.scrollBarPadElement
      ]
    }, {
      // `notranslate` prevents google translate messing up the DOM, https://github.com/facebook/react/issues/11538
      className: "b-weeks-container notranslate",
      reference: "weeksElement",
      children: weeksContainerChildren,
      role: "grid"
    }];
    for (let i = 0; i < 6; i++) {
      const weekRow = {
        role: "row",
        className: "b-calendar-row b-calendar-week",
        dataset: {
          rowIndex: i
        },
        children: [{
          className: "b-week-number-cell",
          role: "rowheader"
        }, {
          className: "b-calendar-days",
          children: [{}, {}, {}, {}, {}, {}, {}],
          syncOptions: {
            ignoreRefs: true,
            strict: false
            // allow complete replacement of classes w/o matching lastDomConfig
          }
        }]
      };
      weeksContainerChildren.push(weekRow);
    }
    return result;
  }
  get firstVisibleDate() {
    if (this.hideOtherMonthCells) {
      const { year, month: month2 } = this.month;
      return new Date(year, month2, 1);
    }
    for (const me = this, date2 = me.month.startDate; ; date2.setDate(date2.getDate() + 1)) {
      if (!me.hideNonWorkingDays || !me.nonWorkingDays[date2.getDay()]) {
        return date2;
      }
    }
  }
  get lastVisibleDate() {
    const lastDate = DateHelper.add(this.endDate, -1, "d");
    if (this.hideOtherMonthCells) {
      return lastDate;
    }
    for (const me = this, date2 = lastDate; ; date2.setDate(date2.getDate() - 1)) {
      if (!me.hideNonWorkingDays || !me.nonWorkingDays[date2.getDay()]) {
        return date2;
      }
    }
  }
  /**
   * Returns the cell associated with the passed date.
   *
   * To exclude dates which are outside of the panel's current month, pass the `strict` parameter as `true`
   * @param {Date|String} date The date to find the element for or a key in the format `YYYY-MM-DD`
   * @param {Boolean} strict Only return the element if this view *owns* the date.
   * @returns {HTMLElement} The cell for the passed date if it exists
   */
  getCell(date2, strict) {
    if (!(typeof date2 === "string")) {
      date2 = DateHelper.makeKey(date2);
    }
    const cell = this.weeksElement.querySelector(`[data-date="${date2}"]`);
    if (cell && (!strict || !cell.classList.contains(this.otherMonthCls))) {
      return cell;
    }
  }
  onTipOverCell({ source: tip, target }) {
    tip.date = DateHelper.parseKey(target.dataset.date);
  }
  updateLocalization() {
    this._localeNonWorkingDays = this._localeWeekends = null;
    this.updateDayNameFormat();
    this.updateWeekStartDay(this.weekStartDay);
    super.updateLocalization();
  }
};
__publicField(CalendarPanel, "$name", "CalendarPanel");
__publicField(CalendarPanel, "type", "calendarpanel");
CalendarPanel.initClass();
CalendarPanel._$name = "CalendarPanel";

// ../Core/lib/Core/widget/Carousel.js
var { abs, ceil, floor: floor2, max: max2, min: min3 } = Math;
var itemCls = "b-carousel-item";
var EMPTY_RANGE = [0, 0];
var INDEX_PROP = Symbol("carouselIndex");
var SYNC_GEN_PROP = Symbol("carouselSyncGen");
var reserveCls = "b-carousel-reserve";
var reserveBeforeCls = `${reserveCls}-before`;
var reserveAfterCls = `${reserveCls}-after`;
var sortByIndex = (a, b) => a[INDEX_PROP] - b[INDEX_PROP];
var visibleFirstCls = "b-carousel-first";
var visibleLastCls = "b-carousel-last";
var visibleSlotCls = "b-carousel-visible";
var xy = ["x", "y"];
var yx = ["y", "x"];
var namesForAxis = {
  h: {
    minSize: "minWidth",
    offsetPos: "offsetLeft",
    offsetSize: "offsetWidth",
    size: "width",
    vxy: ["vx", "vy"],
    xy
  },
  v: {
    minSize: "minHeight",
    offsetSize: "offsetHeight",
    size: "height",
    vxy: ["vy", "vx"],
    xy: yx
  }
};
namesForAxis.h.other = namesForAxis.v;
namesForAxis.v.other = namesForAxis.h;
var Carousel = class extends Panel.mixin(Fencible_default, InfinityScrollable_default) {
  compose() {
    const { carouselClasses } = this;
    return {
      class: {
        ...carouselClasses
      }
    };
  }
  afterFloatReparent() {
    this.syncSoon();
  }
  //region properties
  get allSlots() {
    return this.ensureItems().values.filter((item) => item.innerItem).sort(sortByIndex);
  }
  get bodyConfig() {
    const { carouselClasses, emptyHtml, emptyText } = this, bodyConfig = super.bodyConfig;
    return ObjectHelper.merge(bodyConfig, {
      className: {
        "b-vbox": 1,
        ...carouselClasses
      },
      children: {
        innerCt: {
          class: {
            "b-carousel-inner-ct": 1,
            "b-box-center": 1
          }
        },
        emptyElement: {
          class: {
            "b-carousel-empty-text": 1,
            "b-box-center": 1
          },
          html: emptyHtml || StringHelper.xss`${emptyText}`
        }
      }
    });
  }
  get carouselClasses() {
    const { empty: empty2, vertical } = this;
    return {
      "b-carousel-empty": empty2,
      "b-carousel-vertical": vertical
    };
  }
  get contentElement() {
    return this.innerCt;
  }
  /**
   * The first visible slot index. This first visible slot can be read or written using this property. Setting
   * `currentIndex` will determine if the transition can be animated by the distance being traveled.
   *
   * See {@link #function-goto}.
   * @prp {Number}
   */
  get currentIndex() {
    return this.indexFromPos(this.scrollPos);
  }
  set currentIndex(index) {
    this.goto(index);
  }
  get focusedSlot() {
    const { innerCt } = this;
    let ret = null, el, parentEl;
    for (el = DomHelper.getActiveElement(); el; el = parentEl) {
      parentEl = el.parentElement;
      if (parentEl === innerCt) {
        ret = Widget.fromElement(el);
        break;
      }
    }
    return ret;
  }
  get infinityScrollerDefaults() {
    const me = this, [axis, other] = me.vertical ? yx : xy;
    return ObjectHelper.merge(super.infinityScrollerDefaults, {
      snap: me.snap && axis,
      [axis]: {
        overflow: "hidden-scroll",
        range: me.scrollRange,
        pos: 0
      },
      [other]: {
        overflow: "hidden"
      }
    });
  }
  get propNames() {
    return namesForAxis[this.vertical ? "v" : "h"];
  }
  get scrollAxis() {
    return this.infinityScroller[this.vertical ? "y" : "x"];
  }
  get scrolling() {
    return this.infinityScroller.scrolling;
  }
  get scrollPos() {
    return this.scrollAxis.pos;
  }
  set scrollPos(v) {
    this.scrollAxis.pos = v;
  }
  get scrollRange() {
    const { range, slotSize } = this;
    return slotSize ? [range[0] * slotSize, range[1] * slotSize] : [0, Infinity];
  }
  get shrinkWrap() {
    const shrinkWrap = this._shrinkWrap;
    return shrinkWrap === "auto" ? DomHelper.getStyleValue(this.element, "position") === "absolute" : shrinkWrap;
  }
  get lastVisibleIndex() {
    var _a5;
    return (_a5 = this.lastVisibleSlot) == null ? void 0 : _a5[INDEX_PROP];
  }
  get visibleSlots() {
    return this.allSlots.filter((it) => it.element.classList.contains(visibleSlotCls));
  }
  //endregion
  //region configs
  // atMax
  updateAtMax(atMax) {
    this.element.classList.toggle("b-carousel-at-max", atMax);
  }
  // atMin
  updateAtMin(atMin) {
    this.element.classList.toggle("b-carousel-at-min", atMin);
  }
  // configureSlot
  changeConfigureSlot(configureSlot) {
    if (typeof configureSlot === "string") {
      const key = `_${configureSlot}Fn`;
      return this[key] || (this[key] = (...a) => this[configureSlot](...a));
    }
    return configureSlot;
  }
  // firstVisibleSlot
  triggerSlotVisibility() {
    const { firstVisibleSlot, lastVisibleSlot } = this;
    this.trigger("slotVisibility", { firstVisibleSlot, lastVisibleSlot });
  }
  updateFirstVisibleSlot(firstVisibleSlot) {
    this.triggerSlotVisibility();
  }
  // lastVisibleSlot
  updateLastVisibleSlot() {
    this.triggerSlotVisibility();
  }
  // range
  changeRange(range) {
    var _a5, _b2;
    range = range ? [(_a5 = range[0]) != null ? _a5 : -Infinity, (_b2 = range[1]) != null ? _b2 : Infinity] : [0, 0];
    if (range[0] !== range[1]) {
      let error;
      if (range[1] < range[0]) {
        error = "denormalized";
      }
      if (error) {
        throw new Error(`Invalid carousel range [${range[0]}, ${range[1]}): ${error}`);
      }
    }
    return range;
  }
  updateRange(range) {
    this.empty = !(range[0] < range[1]);
    this.syncSoon();
  }
  // shrinkWrap
  updateShrinkWrap() {
    this.isPainted && this.syncSoon();
  }
  // vertical
  updateVertical() {
    this.isPainted && this.syncSoon();
  }
  //endregion
  //region misc
  convertIndexPos(value2, posToIndex) {
    const me = this, { allSlots, propNames } = me, [indexMin, indexMax] = me.range, midRange = Math.floor((indexMin + indexMax) / 2), slotCount = allSlots.length, lastSlot = allSlots[slotCount - 1], vxy = propNames.vxy[0], zeroInRange = indexMin <= 0 && 0 < indexMax;
    if (!posToIndex && !(indexMin <= value2 && value2 < indexMax)) {
      return null;
    }
    let nominalSize = me.slotSize || 100, begin, end, index, indexLo, indexHi, posHi, posLo, slot;
    if (!slotCount) {
      if (posToIndex && 0 <= value2 && value2 < nominalSize) {
        return zeroInRange ? 0 : midRange;
      }
      posLo = 0;
      posHi = nominalSize;
      indexLo = zeroInRange ? 0 : midRange;
      indexHi = indexLo + 1;
    } else {
      nominalSize = 0;
      for (slot of allSlots) {
        index = slot[INDEX_PROP];
        begin = slot[vxy];
        nominalSize = nominalSize || slot.$vxy[propNames.size];
        if (slot === allSlots[0]) {
          indexLo = index;
          posLo = begin;
          if (value2 < (posToIndex ? begin : index)) {
            break;
          }
        }
        if (posToIndex) {
          if (value2 === begin) {
            return index;
          }
          if (value2 < begin) {
            return index - 1;
          }
        } else if (value2 === index) {
          return begin;
        }
        if (slot === lastSlot) {
          end = begin + slot.$vxy[propNames.size];
          if (posToIndex && value2 < end) {
            return index;
          }
          indexHi = index + 1;
          posHi = end;
        }
      }
      nominalSize = nominalSize || me.slotSize || 100;
    }
    if (posToIndex) {
      return value2 < posLo ? max2(indexLo - ceil((posLo - value2) / nominalSize), indexMin) : min3(indexHi + floor2((value2 - posHi) / nominalSize), indexMax);
    }
    return value2 < indexLo ? posLo - (indexLo - value2) * nominalSize : posHi + (value2 - indexHi) * nominalSize;
  }
  /**
   * Decodes any `options` specified to a derived class that may be passed to {@link #function-ensureVisible}. This
   * method ensures the `options` object has the properties needed by {@link #function-ensureVisible}.
   * @param {Object} options Options to configure which slot should be visible and how to scroll if necessary.
   * @param {Number} options.index The index of the slot.
   * @param {Boolean} [options.animate] Pass `false` to disable scroll animation.
   * @returns {Object}
   * @internal
   */
  ensurePlan(options) {
    return options;
  }
  /**
   * Ensures that the given slot `index` is visible, scrolling if necessary to make it so.
   * @param {Object} options Options to configure which slot should be visible and how to scroll if necessary.
   * @param {Number} options.index The index of the slot.
   * @param {Boolean} [options.animate] Pass `false` to disable scroll animation.
   */
  ensureVisible(options) {
    const me = this;
    if (me.lastScrollPos == null) {
      me._afterSync = () => me.ensureVisible({ ...options, animate: false });
    } else if (me.scrolling && me.hasPainted) {
      me._afterScroll = () => me.ensureVisible(options);
    } else {
      const { index, animate } = me.ensurePlan(options), { currentIndex, lastVisibleIndex } = me;
      let go;
      if (index < currentIndex) {
        go = index - currentIndex;
      } else if (lastVisibleIndex < index) {
        go = index - lastVisibleIndex;
      }
      return go && me.go(go, animate);
    }
  }
  getSlotIndex(item) {
    return item == null ? void 0 : item[INDEX_PROP];
  }
  /**
   * Returns the carousel slot index given a logical scroll position (see {@link Core.helper.util.InfinityScroller}).
   * @param {Number} pos The logical scroll position from the main {@link Core.helper.util.InfinityAxis}.
   * @returns {Number}
   */
  indexFromPos(pos) {
    return this.convertIndexPos(pos, true);
  }
  /**
   * Returns the logical scroll position (see {@link Core.helper.util.InfinityScroller}) given a carousel slot index.
   * @param {Number} index The carousel slot index.
   * @returns {Number}
   */
  posFromIndex(index) {
    return this.convertIndexPos(index, false);
  }
  slotFromIndex(index) {
    for (const slot of this.allSlots) {
      if (slot[INDEX_PROP] === index) {
        return slot;
      }
    }
    return null;
  }
  //endregion
  //region navigation
  /**
   * Step the carousel back one slot index.
   * @param {Boolean} [animate] Pass `false` to disable smooth scrolling animation. Animations can only be disabled
   * with this parameter, not enabled. For more details, see {@link #function-go}
   */
  backward(animate) {
    return this.go(-1, animate);
  }
  /**
   * Step the carousel forward one slot index. See {@link #function-go}.
   * @param {Boolean} [animate] Pass `false` to disable smooth scrolling animation. Animations can only be disabled
   * with this parameter, not enabled. For more details, see {@link #function-go}
   */
  forward(animate) {
    return this.go(1, animate);
  }
  /**
   * Steps the slot index of the carousel forward or backward by the given `increment`.
   * @param {Number} increment The number of slot indexes to advance forward (`> 0`) or backward (`< 0`).
   * @param {Boolean} [animate] Pass `false` to disable smooth scrolling animation. Animations can only be disabled
   * with this parameter, not enabled. For more details, see {@link #function-goto}
   */
  go(increment, animate) {
    return this.goto(this.currentIndex + increment, animate);
  }
  /**
   * Sets the slot index of the carousel to the given `index`.
   * @param {Number} index The new slot index for the carousel.
   * @param {Boolean} [animate] Pass `false` to disable smooth scrolling animation. Animations can only be disabled
   * with this parameter, not enabled. Animations are always disabled if
   * {@link Core.helper.util.InfinityScrollable#property-animate} is set to `false`. Further, if the slot index is
   * changing by a large enough amount, animations will be also disabled. In other words, smooth scroll animation will
   * be used as long as none of these conditions occur.
   */
  goto(index, animate) {
    const me = this;
    if (me.lastScrollPos == null) {
      me._afterSync = () => {
        const { scrollPos } = me;
        me.goto(index, false);
        if (!me.hasPainted && scrollPos !== me.scrollPos) {
          me.sync();
        }
      };
    } else if (me.scrolling && me.hasPainted) {
      me._afterScroll = () => me.goto(index, animate);
    } else {
      const { range, slots } = me, indexMax = range[1] - (typeof slots === "number" ? slots - 1 : 0), indexClip = min3(indexMax - 1, max2(range[0], index)), delta = abs(me.currentIndex - indexClip), pos = me.posFromIndex(indexClip);
      if (delta >= me.allSlots.length) {
        animate = false;
      }
      return me.scrollTo({ animate, [me.scrollAxis.name]: pos });
    }
  }
  //endregion
  onChildAdd(item) {
    item.innerItem && item.element.classList.add(itemCls);
    return super.onChildAdd(item);
  }
  onChildRemove(item) {
    item.innerItem && item.element.classList.remove(itemCls);
    return super.onChildRemove(item);
  }
  onInfiniteScrollEnd(ev) {
    super.onInfiniteScrollEnd(ev);
    const { _afterScroll } = this;
    this._afterScroll = null;
    _afterScroll == null ? void 0 : _afterScroll();
  }
  onSlotFocusTrap({ source, top }) {
    const me = this, delta = top ? -1 : 1, index = source[INDEX_PROP], slot = me.slotFromIndex(index + delta);
    if (!slot.element.classList.contains(reserveCls)) {
      slot.element.focus();
    } else if (me.scrollOnTab) {
      me.go(delta).then((success2) => {
        var _a5;
        if (success2) {
          (_a5 = slot.element) == null ? void 0 : _a5.focus();
        }
      });
    }
    return false;
  }
  //region sync
  resync() {
    this.sync();
  }
  /**
   * Synchronizes the scroll range and child items based on the current configuration and scroll position. This is
   * called in response to scroll events and when the carousel is reconfigured.
   * @internal
   */
  sync() {
    var _a5;
    this.syncCount = (this.syncCount || 0) + 1;
    if (!this.empty) {
      const me = this, { allSlots, disableReserveSlots, innerCt, scrollAxis, propNames, reserveSlots, scrolling, slots } = me, [indexMin, indexMax] = me.range, scrollPos = scrollAxis.pos, defaultType = Widget.resolveType(((_a5 = me.defaults) == null ? void 0 : _a5.type) || me.defaultType), indexOfPos = me.indexFromPos(scrollPos), posOfIndex = me.posFromIndex(indexOfPos), offSnap = scrollPos - posOfIndex, reserve = [reserveSlots, reserveSlots], syncGen = me.syncGen = (me.syncGen || 0) + 1;
      if (scrolling && scrollPos > (me.lastScrollPos || 0) && offSnap) {
        --reserve[0];
        ++reserve[1];
      }
      me.lastScrollPos = scrollPos;
      innerCt.style.setProperty("--carousel-item-offset", `${Math.abs(offSnap)}px`);
      if (!me.shrinkWrapped) {
        innerCt.style[propNames.minSize] = "";
      }
      let space = innerCt[propNames.offsetSize], atMax = false, atMin = false, firstSlot = null, lastSlot = null, classList, cls, classes, slot;
      me.unanimated(() => {
        const { _afterSync } = me, item = slots && slots !== "auto" ? me.syncFixed(indexOfPos, propNames, allSlots, defaultType, reserve, slots, indexMin, indexMax) : me.syncDynamic(indexOfPos, propNames, allSlots, defaultType, reserve, slots, space, indexMin, indexMax), classSync = [], cleanup = allSlots.filter((slot2) => slot2[SYNC_GEN_PROP] !== syncGen);
        if (cleanup.length) {
          cleanup.forEach((slot2) => me.detachListeners(`${slot2.id}-listeners`));
          me.remove(cleanup);
        }
        innerCt.style[propNames.other.minSize] = `${item[propNames.other.size]}px`;
        space = innerCt[propNames.offsetSize];
        for (slot of allSlots) {
          if (!cleanup.includes(slot)) {
            const span = scrollAxis.getItemSpan(slot), end = scrollPos + space, before = span[1] <= scrollPos, after = end <= span[0], visible = !before && !after, first = visible && span[0] <= scrollPos, last = visible && span[1] >= end;
            if (visible) {
              if (slot[INDEX_PROP] === indexMin) {
                atMin = true;
              }
              if (slot[INDEX_PROP] === indexMax - 1) {
                atMax = true;
              }
            }
            if (first) {
              firstSlot = slot;
            }
            if (last) {
              lastSlot = slot;
            }
            classSync.push([
              // gather class updates to avoid reflow due to write/read cycles
              slot,
              slot.element.classList,
              {
                [reserveCls]: before || after,
                [reserveBeforeCls]: before,
                [visibleFirstCls]: first,
                [visibleSlotCls]: visible,
                [visibleLastCls]: last,
                [reserveAfterCls]: after
              }
            ]);
          }
        }
        for ([slot, classList, classes] of classSync) {
          if (disableReserveSlots) {
            slot.disabled = classes[visibleSlotCls] ? false : disableReserveSlots;
          }
          for (cls in classes) {
            classList.toggle(cls, classes[cls]);
          }
        }
        me.atMax = atMax;
        me.atMin = atMin;
        me.firstVisibleSlot = firstSlot;
        me.lastVisibleSlot = lastSlot;
        scrollAxis.other.range = EMPTY_RANGE;
        me.syncDomOrder(cleanup.length ? me.allSlots : allSlots);
        !scrolling && me.scrollable.fixPositionSync();
        scrollAxis.sync({
          range: me.scrollRange
        });
        if (scrollAxis.pos !== scrollPos) {
          me.resync();
        } else if (_afterSync) {
          me._afterSync = null;
          _afterSync();
        }
      });
    }
  }
  syncDomOrder(allSlots) {
    const activeElement = DomHelper.getActiveElement();
    let el, el2, i;
    for (i = allSlots.length; i-- > 1; ) {
      el2 = allSlots[i].element;
      if (!el) {
        el2.classList.add("b-last-visible-child");
      }
      el = allSlots[i - 1].element;
      el.classList.remove("b-last-visible-child");
      el2.classList.remove("b-first-visible-child");
      el.nextSibling !== el2 && el.parentElement.insertBefore(el, el2);
    }
    el == null ? void 0 : el.classList.add("b-first-visible-child");
    if (this.element.contains(activeElement) && activeElement !== DomHelper.getActiveElement()) {
      activeElement.focus();
    }
  }
  syncDynamic(indexOfPos, propNames, allSlots, defaultType, reserve, slots, space, indexMin, indexMax) {
    const me = this, { offsetSize } = propNames, auto = slots === "auto", clearSize = auto ? "" : null;
    let [extraBefore, extra] = reserve, advance = 1, filled = 0, firstItem, item, slotIndex;
    for (slotIndex = indexOfPos; indexMin <= slotIndex && slotIndex < indexMax; slotIndex += advance) {
      item = me.syncSlot(allSlots, defaultType, slotIndex, clearSize);
      firstItem = firstItem || item;
      if (filled < space) {
        filled += item.element[offsetSize];
        if (auto) {
          return me.syncFixed(indexOfPos, propNames, allSlots, defaultType, reserve, ceil(space / filled));
        }
        if (advance > 0) {
          if (slotIndex + 1 < indexMax) {
            if (filled < space || extra) {
              continue;
            }
          }
          extraBefore += extra;
          extra = 0;
        }
      }
      if (--extra < 1) {
        if (advance < 0 || !extraBefore) {
          break;
        }
        advance = -1;
        slotIndex = indexOfPos;
        extra = extraBefore;
      }
    }
    return firstItem;
  }
  syncFixed(indexOfPos, propNames, allSlots, defaultType, reserve, slots, indexMin, indexMax) {
    const me = this, { shrinkWrap, shrinkWrapped } = me, itemSize = `${100 / slots}%`, indexOfEnd = indexOfPos + slots;
    let beginIndex = indexOfPos - reserve[0], endIndex = indexOfEnd + reserve[1], size = 0, firstItem, item, items2, lastVisibleIndex, slotIndex;
    while (endIndex > indexMax) {
      --beginIndex;
      --endIndex;
    }
    while (beginIndex < indexMin) {
      ++beginIndex;
      ++endIndex;
    }
    for (slotIndex = beginIndex; slotIndex < endIndex; ++slotIndex) {
      item = me.syncSlot(allSlots, defaultType, slotIndex, shrinkWrap && !shrinkWrapped ? "" : itemSize);
      if (slotIndex === indexOfPos) {
        firstItem = item;
      }
      if (shrinkWrap) {
        (items2 || (items2 = [])).push(item);
      } else {
        item[propNames.size] = itemSize;
      }
    }
    if (shrinkWrap) {
      lastVisibleIndex = min3(indexOfEnd, endIndex - reserve[1]);
      for (slotIndex = lastVisibleIndex - slots; slotIndex < lastVisibleIndex; ++slotIndex) {
        size += items2[slotIndex - beginIndex][propNames.size];
      }
      for (item of items2) {
        item[propNames.size] = itemSize;
      }
      if (size) {
        me.innerCt.style[propNames.minSize] = `${size}px`;
        me.shrinkWrapped = true;
      }
    }
    return firstItem;
  }
  syncSlot(allSlots, defaultType, slotIndex, itemSize) {
    const me = this, { propNames, syncGen } = me, changeSize = itemSize !== null;
    let reuse = null, config, index, slot, it, type;
    for (it of allSlots) {
      if (it[SYNC_GEN_PROP] !== syncGen) {
        index = it[INDEX_PROP];
        if (index === slotIndex) {
          slot = it;
          if (changeSize) {
            slot[propNames.size] = itemSize;
          }
          break;
        }
        if (!reuse || index > reuse[INDEX_PROP]) {
          reuse = it;
        }
      }
    }
    if (!slot) {
      config = me.configureSlot(slotIndex, me, reuse);
      if (reuse) {
        slot = reuse;
        if (changeSize) {
          slot[propNames.size] = itemSize;
        }
        delete config.type;
        slot.setConfig(config);
      } else {
        type = config.type ? Widget.resolveType(config.type) : defaultType;
        config = type.mergeConfigs(me.defaults, config);
        if (changeSize) {
          config[propNames.size] = itemSize;
        }
        slot = me.add(config);
        allSlots.push(slot);
        slot.ion({
          thisObj: me,
          name: `${slot.id}-listeners`,
          focusTrap: "onSlotFocusTrap"
        });
      }
      slot[INDEX_PROP] = slotIndex;
      if (!me.slotSize) {
        me.slotSize = slot[propNames.size];
      }
      slot[propNames.vxy[0]] = slotIndex * me.slotSize;
    }
    slot[SYNC_GEN_PROP] = syncGen;
    return slot;
  }
  //endregion
};
__publicField(Carousel, "$name", "Carousel");
__publicField(Carousel, "type", "carousel");
__publicField(Carousel, "configurable", {
  atMax: null,
  atMin: null,
  /**
   * A callback function that must be provided by the developer to produce configuration objects for the slots
   * in the carousel.
   *
   * Derived classes may set this config to the name of the instance method to call. The `this` pointer will be
   * the carousel instance.
   *
   * @prp {String|Function}
   * @param {Number} index The slot index as defined by the {@link #config-range} of the carousel.
   * @param {Core.widget.Carousel} carousel The carousel instance.
   * @param {Core.widget.Widget} [slot] The slot being reconfigured or `null` when creating a new slot.
   * @returns {ContainerItemConfig} The slot's configuration object.
   */
  configureSlot: (index, carousel, slot) => ({}),
  /**
   * The value to set for the {@link Core.widget.Widget#config-disabled} config on reserve slots. These slots
   * are not visible to the user but can be tabbed into from visible slots.
   *
   * When set to `'inert'`, the `inert` DOM attribute is also set. This prevents tabbing from a visible slot to
   * a reserved slot.
   * @default false
   * @config {Boolean|'inert'}
   */
  disableReserveSlots: null,
  // private
  empty: null,
  /**
   * The HTML to render when the carousel's {@link #config-range} is empty. To avoid XSS attacks, it is safer to
   * use {@link #config-emptyText} instead.
   * @prp {String}
   */
  emptyHtml: null,
  /**
   * The text to render when the carousel's {@link #config-range} is empty. This text is automatically encoded
   * and is safe from XSS issues. To render markup when the carousel is empty, see {@link #config-emptyHtml}.
   * @prp {String}
   * @default
   */
  emptyText: "No items to display",
  firstVisibleSlot: null,
  lastVisibleSlot: null,
  /**
   * The range of slot indexes to be presented in the carousel. Slot indexes greater than or equal to `range[0]`
   * and less than `range[1]` will be rendered.
   *
   * If this value is `null` or `[0, 0]`, the carousel will render no items (i.e., it will be empty). See
   * {@link #config-emptyText}.
   *
   * By default, the carousel's range is unlimited, i.e., `range = [-Infinity, Infinity]`.
   *
   * @prp {Number[]}
   */
  range: [null, null],
  /**
   * The number of slots to render beyond what is visible in the carousel. These reserve items are critical to
   * giving the appearance of continuity as the carousel is being scrolled. A minimum of 1 is required and is
   * generally sufficient for slots that are reasonably large. If slots are small (maybe less than 100px), it
   * may be helpful to provide additional reserve slots.
   * @prp {Number}
   * @default
   */
  reserveSlots: 1,
  /**
   * Set to `true` to use {@link Core.widget.Panel#config-trapFocus} to scroll the carousel when tabbing to slots
   * that are not currently visible.
   *
   * NOTE: This only works when {@link #config-configureSlot} produces {@link Core.widget.Panel} instances.
   * @prp {Boolean}
   * @default false
   */
  scrollOnTab: null,
  /**
   * Due to the `position: absolute` nature of carousel items, a carousel does not have a natural size. When the
   * carousel is being sized by its container, this is not a problem, but can be an issue if the carousel is being
   * used in a layout where its natural size would be important.
   *
   * Specify `true` for the carousel to provide a natural size based on the size of its slots. This is done by
   * setting `min-width` and/or `min-height` on the internal elements that contain the absolutely positioned
   * slots. This may have a modest performance impact due to the required measuring of the slots to provide
   * these minimums.
   *
   * By default (`shrinkWrap = 'auto'`), the carousel enables shrinkWrap support when the carousel's main
   * element is `position: absolute`. This can be disabled by setting `shrinkWrap = false`.
   * @prp {Boolean|'auto'}
   * @default
   */
  shrinkWrap: "auto",
  /**
   * The number of slots to present in the carousel. When set to a number, each slot is given a percentage size
   * (e.g., "50%" for `slots = 2`) so they fill the carousel. This is typically required when the carousel is
   * using {@link #config-shrinkWrap} (note: shrink wrapping is enabled by default for `position: absolute`
   * carousels).
   *
   * When `null`, as many slots as needed to fill the carousel are created. This can produce partially visible
   * slots.
   *
   * Setting this value to `'auto'` will similarly fill the available space, but once the required number of slots
   * is determined, they are assigned a percentage size so that they completely fill the carousel.
   *
   * @prp {Number}
   */
  slots: null,
  /**
   * Set to `false` to disable scroll snapping and allow free scrolling through the carousel.
   * @prp {Boolean}
   * @default
   */
  snap: true,
  /**
   * Set to `true` to enable vertical mode. By default, carousels scroll horizontally.
   * @prp {Boolean}
   * @default false
   */
  vertical: null
});
__publicField(Carousel, "delayable", {
  triggerSlotVisibility: "raf"
});
__publicField(Carousel, "fenced", {
  resync: true
});
Carousel.initClass();
Carousel._$name = "Carousel";

// ../Core/lib/Core/widget/Checkbox.js
var whenNotChecked = (field) => !field.value;
var Checkbox = class extends Field {
  static get configurable() {
    return {
      inputType: "checkbox",
      /**
       * Specify `true` to automatically {@link Core.widget.FieldContainer#config-collapsed collapse} the field's
       * {@link #config-container} when the field is not {@link #property-checked}.
       *
       * Alternatively, this can be a function that returns the desired `collapse` state when passed the field
       * instance as its one parameter.
       *
       * @config {Boolean|Function}
       * @returns {Boolean}
       * @default false
       */
      autoCollapse: null,
      toggleGroupRootElement: null,
      containerDefaults: {
        syncableConfigs: {
          disabled: (field) => field.disabled || !field.value
        },
        syncConfigTriggers: {
          autoCollapse: 1,
          value: 1
        }
      },
      /**
       * Get/set label
       * @member {String} name
       */
      /**
       * Text to display on checkbox label
       * @config {String}
       */
      text: "",
      /**
       * The value to provide for this widget in {@link Core.widget.Container#property-values} when it is
       * {@link #property-checked}.
       * A value of `undefined` will cause this widget not to include its value when checked.
       * @config {*}
       * @default
       */
      checkedValue: true,
      /**
       * The value to provide for this widget in {@link Core.widget.Container#property-values} when it is not
       * {@link #property-checked}.
       *
       * A value of `undefined` will cause this widget to not include its value when it is unchecked.
       * @config {*}
       * @default
       */
      uncheckedValue: false,
      /**
       * The checked state. The same as `value`.
       * @prp {Boolean} checked
       */
      /**
       * Checkbox color, must have match in CSS
       * @config {String}
       */
      color: null,
      /**
       * The value of the checkbox. This is the same as `checked`.
       * @prp {Boolean}
       */
      value: false,
      toggleGroup: null,
      localizableProperties: ["label", "text"],
      twinSyncConfigs: {
        value: "checked"
      }
    };
  }
  //endregion
  //region Init
  construct(config) {
    if ("checked" in config) {
      config = ObjectHelper.assign({}, config);
      config.value = config.checked;
      delete config.checked;
    }
    super.construct(config);
    this.syncHasText();
  }
  updateClearable() {
  }
  updateEditable() {
  }
  get textLabelCls() {
    return "b-checkbox-label";
  }
  // Implementation needed at this level because it has two inner elements in its inputWrap
  get innerElements() {
    return [
      this.inputElement,
      {
        tag: "label",
        class: this.textLabelCls,
        for: `${this.id}-input`,
        reference: "textLabel",
        html: this.text || "",
        "aria-hidden": !this.text
      }
    ];
  }
  get inputElement() {
    const config = super.inputElement;
    if (this.toggleGroup) {
      config.dataset = {
        group: this.toggleGroup
      };
    }
    config.listeners = {
      click: "internalOnClick",
      change: "internalOnChange",
      input: "internalOnInput"
    };
    return config;
  }
  //endregion
  //region Toggle
  get checked() {
    return this.value;
  }
  set checked(value2) {
    this.value = value2;
  }
  syncHasText() {
    var _a5;
    this.element.classList[this.text ? "add" : "remove"]("b-text");
    (_a5 = this.textLabel) == null ? void 0 : _a5.setAttribute("aria-hidden", !this.text);
  }
  updateText(value2) {
    if (this.textLabel) {
      this.syncHasText();
      this.textLabel.innerHTML = value2;
    }
  }
  afterSyncChildConfigs(container) {
    super.afterSyncChildConfigs(container);
    let { autoCollapse } = this;
    if (autoCollapse) {
      autoCollapse = autoCollapse === true ? whenNotChecked : autoCollapse;
      container.collapsed = autoCollapse(this);
    }
  }
  assignFieldValue(values, key, value2) {
    this.value = value2 === this.checkedValue || (value2 === this.uncheckedValue ? false : null);
  }
  fetchInputValue() {
    if (!this.readOnly) {
      this.value = this.input.checked;
    }
  }
  gatherValue(values) {
    var _a5;
    const me = this, value2 = me.value ? me.checkedValue : me.uncheckedValue, storedValue = value2 !== void 0, { valueName } = me;
    if (storedValue) {
      values[valueName] = value2;
    }
    me.gatherValues(values, storedValue);
    if (value2 === true && ((_a5 = values[valueName]) == null ? void 0 : _a5.value) === value2) {
      delete values[valueName].value;
    }
  }
  changeValue(value2) {
    return value2 === "false" ? false : Boolean(value2);
  }
  updateValue(value2) {
    var _a5;
    const me = this, changed = me.input.checked !== value2;
    me.input.checked = value2;
    (_a5 = me.container) == null ? void 0 : _a5.syncChildConfigs();
    if (changed && !me.inputting && !me.isConfiguring) {
      me.uncheckToggleGroupMembers();
      me.triggerChange(false, null);
    }
  }
  get inputValueAttr() {
    return "checked";
  }
  updateColor(value2, was) {
    const classes = this.element.classList;
    if (was) {
      classes.remove(was);
    }
    if (value2) {
      classes.add(value2);
    }
  }
  getToggleGroupRootElement() {
    const value2 = this.toggleGroupRootElement;
    let result;
    if (value2 && typeof value2 === "string" && this.element) {
      result = this.element.closest(value2);
    }
    return result || this.rootElement;
  }
  getToggleGroupMembers() {
    const me = this, { toggleGroup, input: checkedElement, type } = me, result = [];
    if (toggleGroup) {
      DomHelper.forEachSelector(me.getToggleGroupRootElement(), `input[type=${type}][data-group=${toggleGroup}]`, (inputEl) => {
        if (inputEl !== checkedElement) {
          const partnerCheckbox = Widget.fromElement(inputEl);
          partnerCheckbox && result.push(partnerCheckbox);
        }
      });
    }
    return result;
  }
  uncheckToggleGroupMembers(userAction) {
    if (this.checked && this.toggleGroup) {
      this.getToggleGroupMembers().forEach((widget) => {
        widget._propagatingUserActionFrom = this;
        widget.checked = false;
        widget._propagatingUserActionFrom = null;
      });
    }
  }
  /**
   * Check the box
   */
  check() {
    this.checked = true;
  }
  /**
   * Uncheck the box
   */
  uncheck() {
    this.checked = false;
  }
  /**
   * Toggle checked state. If you want to force a certain state, assign to {@link #property-checked} instead.
   */
  toggle() {
    this.checked = !this.checked;
  }
  //endregion
  //region Events
  internalOnClick(event) {
    if (this.readOnly) {
      event.preventDefault();
      return false;
    }
    return this.trigger("click", { event });
  }
  /**
   * Triggers events when user toggles the checkbox
   * @fires beforeChange
   * @fires change
   * @fires action
   * @private
   */
  internalOnChange(event) {
    const me = this;
    if (me.readOnly && me.value !== me.input.checked) {
      me.input.checked = me.value;
      return;
    }
    me.value = me.input.checked;
    if (!me.inputting) {
      me.inputting = true;
      me.triggerChange(true, event);
      me.inputting = false;
    }
  }
  // Need to catch changes even if readOnly, because of chrome behaviour when re-enabling
  updateInputReadOnly(readOnly) {
  }
  /**
   * Triggers events when checked state is changed
   * @fires beforeChange
   * @fires change
   * @fires action
   * @private
   */
  triggerChange(userAction, event) {
    const me = this, { checked } = me.input;
    const eventObject = { checked, value: checked, oldValue: !checked, userAction, valid: true, event };
    if (me._propagatingUserActionFrom) {
      eventObject.propagatingUserActionFrom = me._propagatingUserActionFrom;
    }
    const prevented = !checked && userAction && me.toggleGroup && me.getToggleGroupMembers().filter((widget) => widget.isVisible && !widget.disabled).length || // Since Widget has Events mixed in configured with 'callOnFunctions' this will also call onBeforeChange,
    // onChange and onAction
    me.trigger("beforeChange", eventObject) === false;
    if (prevented) {
      me.input.checked = me._value = !checked;
    } else {
      me.triggerFieldChange(eventObject, false);
      if (userAction) {
        me.uncheckToggleGroupMembers(userAction);
      }
      me.trigger("action", eventObject);
      me.trigger("change", eventObject);
      return true;
    }
  }
  //endregion
  configureOverflowTwin(overrides) {
    const me = this, config = super.configureOverflowTwin(overrides);
    config.type = Widget.resolveType("menuitem");
    if (!config.text) {
      config.text = me.label || me.text || me.tooltipText;
    }
    return config;
  }
};
//region Config
/**
 * @hideconfigs clearable, placeholder, editable
 */
__publicField(Checkbox, "$name", "Checkbox");
__publicField(Checkbox, "type", "checkbox");
__publicField(Checkbox, "alias", "check");
Checkbox.initClass();
Checkbox._$name = "Checkbox";

// ../Core/lib/Core/widget/FieldSet.js
var FieldSet = class extends Panel.mixin(Labelable_default) {
  static get prototypeProperties() {
    return {
      flexRowCls: "b-hbox",
      flexColCls: "b-vbox"
    };
  }
  //endregion
  //region Composition
  get bodyConfig() {
    const result = super.bodyConfig, { className } = result, { inlineInternal: inline, hasLabel, title } = this;
    delete result.html;
    className["b-inline"] = inline;
    className["b-fieldset-has-label"] = hasLabel;
    if (title) {
      result.children = {
        // We render the <legend> element for a11y (not 100% sure it is needed)
        legendElement: {
          tag: "legend",
          text: title,
          class: {
            "b-fieldset-legend": 1
          }
        }
      };
    }
    return result;
  }
  compose() {
    const { inlineInternal: inline, label, labelCls, labelWidth } = this;
    return {
      class: {
        "b-field": label,
        "b-vbox": !inline
        // override panel
      },
      children: {
        "labelElement > headerElement": (label || null) && {
          tag: "label",
          html: label,
          class: {
            "b-label": 1,
            "b-align-start": 1,
            [labelCls]: labelCls
          },
          style: {
            width: DomHelper.unitize("width", labelWidth)[1]
          }
        }
      }
    };
  }
  //endregion
  syncInlineInternal() {
    var _a5;
    this.inlineInternal = (_a5 = this.inline) != null ? _a5 : this.label != null && this.labelPosition === "before";
  }
  updateDisabled(value2, was) {
    super.updateDisabled(value2, was);
    this.eachWidget(
      (item) => {
        item.disabled = value2;
      },
      /* deep = */
      false
    );
  }
  updateInline() {
    this.syncInlineInternal();
  }
  updateInlineInternal(inline) {
    this.layout.horizontal = inline;
  }
  updateLabel() {
    this.syncInlineInternal();
  }
  updateLabelPosition() {
    this.syncInlineInternal();
  }
};
//region Config
__publicField(FieldSet, "$name", "FieldSet");
__publicField(FieldSet, "type", "fieldset");
__publicField(FieldSet, "configurable", {
  bodyTag: "fieldset",
  focusable: false,
  localizableProperties: ["label"],
  /**
   * Setting this config to `true` assigns a horizontal box layout (`flex-flow: row`) to the items in this
   * container, while `false` assigns a vertical box layout (`flex-flow: column`).
   *
   * By default, this value is automatically determined based on the {@link #config-label} and
   * {@link #config-labelPosition} configs.
   * @prp {Boolean}
   */
  inline: null,
  inlineInternal: null,
  layout: {
    type: "box",
    horizontal: false
  }
});
FieldSet.initClass();
FieldSet._$name = "FieldSet";

// ../Core/lib/Core/widget/Radio.js
var Radio = class extends Checkbox {
  static get configurable() {
    return {
      inputType: "radio",
      /**
       * Set this to `true` so that clicking a checked radio button will clear its checked state.
       * @config {Boolean}
       * @default false
       */
      clearable: null,
      uncheckedValue: void 0
      // won't store to Container#values when unchecked
    };
  }
  //endregion
  //region Init
  get textLabelCls() {
    return super.textLabelCls + " b-radio-label";
  }
  //endregion
  internalOnClick(info) {
    if (super.internalOnClick(info) !== false) {
      if (this.checked && this.clearable) {
        this.checked = false;
      }
    }
  }
  updateName(name) {
    this.toggleGroup = name;
  }
  // Empty override to get rid of clear trigger
  updateClearable() {
  }
};
//region Config
__publicField(Radio, "$name", "Radio");
__publicField(Radio, "type", "radio");
__publicField(Radio, "alias", "radiobutton");
Radio.initClass();
Radio._$name = "Radio";

// ../Core/lib/Core/widget/RadioGroup.js
var RadioGroup = class extends FieldSet {
  triggerPaint() {
    const me = this, firstPaint = !me.isPainted, value2 = me.__value;
    super.triggerPaint(...arguments);
    if (BrowserHelper.isFirefox && me.isVisible && firstPaint && value2 !== void 0 && value2 !== me.value) {
      me.value = void 0;
      me.value = value2;
    }
  }
  get existingOptions() {
    const { name } = this;
    return this.ensureItems().filter((c) => c.name === name);
  }
  get refPrefix() {
    return `${this.name || this.ref || this.id}_`;
  }
  get selected() {
    return this.existingOptions.filter((c) => c.input.checked)[0] || null;
  }
  /**
   * This property corresponds to the {@link Core.widget.Radio#config-checkedValue} of the currently
   * {@link Core.widget.Radio#property-checked} radio button.
   * @prp {String}
   * @typings {String|String[]}  // Fix for value type in CheckboxGroup
   */
  get value() {
    const { selected } = this;
    return selected ? selected.checkedValue : null;
  }
  set value(v) {
    this.existingOptions.forEach((c) => {
      c.isConfiguring = this.isConfiguring;
      c.checked = c.checkedValue === v;
      c.isConfiguring = false;
    });
    if (BrowserHelper.isFirefox && !this.isConnected) {
      this.__value = v;
    }
  }
  ensureItems() {
    this.getConfig("options");
    return super.ensureItems();
  }
  changeOptions(options, was) {
    if (!(options && was && ObjectHelper.isDeeplyEqual(was, options))) {
      return options;
    }
  }
  convertOption(key, option, existing) {
    const me = this, { name } = me, ret = {
      name,
      type: me.defaultType,
      value: me.peekConfig("value"),
      ref: `${me.refPrefix}${key}`,
      disabled: me.disabled,
      checkedValue: key
    };
    if (typeof option === "string") {
      ret.text = option;
    } else {
      ObjectHelper.assign(ret, option);
    }
    return existing ? Widget.reconfigure(existing, ret) : ret;
  }
  isOurRadio(item) {
    return (item.isRadio || item.isCheckbox) && item.name === this.name;
  }
  isolateFieldChange(field) {
    return this.isOurRadio(field);
  }
  onChildAdd(item) {
    super.onChildAdd(item);
    if (this.isOurRadio(item)) {
      item.ion({
        name: item.id,
        beforeChange: "onItemBeforeChange",
        change: "onItemChange",
        click: "onItemClick",
        thisObj: this
      });
    }
  }
  onChildRemove(item) {
    if (this.isOurRadio(item)) {
      this.detachListeners(item.id);
    }
    super.onChildRemove(item);
  }
  onItemClick(ev) {
    const { source } = ev;
    if (source.checked && this.clearable && source.clearable == null) {
      source.checked = false;
    }
  }
  onItemBeforeChange(ev) {
    if (ev.checked) {
      const me = this, { lastValue } = me;
      if (!me.reverting && me.trigger("beforeChange", me.wrapRadioEvent(ev)) === false) {
        if (lastValue != null && lastValue !== me.value) {
          me.reverting = true;
          ev.source.uncheckToggleGroupMembers();
          me.value = lastValue;
          me.lastValue = lastValue;
          me.reverting = false;
          return false;
        }
      }
    }
  }
  onItemChange(ev) {
    const me = this;
    if (ev.checked && !me.reverting) {
      me.triggerFieldChange(me.wrapRadioEvent(ev));
      me.lastValue = me.value;
    }
  }
  wrapRadioEvent(ev) {
    return {
      from: ev,
      item: ev.source,
      userAction: ev.userAction,
      lastValue: this.lastValue,
      value: this.value
    };
  }
  updateOptions() {
    const me = this, { options, refPrefix } = me, existingOptions = me.existingOptions.reduce((m, c) => {
      m[c.ref.substring(refPrefix.length)] = c;
      return m;
    }, {});
    let index = 0, key, option;
    if (options) {
      for (key in options) {
        option = me.convertOption(key, options[key], existingOptions[key]);
        delete existingOptions[key];
        me.insert(option, index++);
      }
    }
    const existing = Object.values(existingOptions);
    if (existing == null ? void 0 : existing.length) {
      me.remove(existing);
      existing.forEach((c) => c.destroy());
    }
  }
};
//region Config
__publicField(RadioGroup, "$name", "RadioGroup");
__publicField(RadioGroup, "type", "radiogroup");
__publicField(RadioGroup, "configurable", {
  defaultType: "radio",
  /**
   * Set this to `true` so that clicking the currently checked radio button will clear the check from all
   * radio buttons in the group.
   * @config {Boolean}
   * @default false
   */
  clearable: null,
  /**
   * The name by which this widget's {@link #property-value} is accessed using the parent container's
   * {@link Core.widget.Container#property-values}.
   *
   * The config must be provided as it is used to set the {@link Core.widget.Radio#config-name} of the
   * child {@link Core.widget.Radio radio buttons}.
   * @config {String}
   */
  name: null,
  /**
   * The set of radio button options for this radio button group. This is a shorthand for defining these in
   * the {@link Core.widget.Container#config-items}. The keys of this object hold the radio button's
   * {@link Core.widget.Radio#config-checkedValue} while the object values are a string for the radio button's
   * {@link Core.widget.Radio#config-text} or a config object for that radio button.
   *
   * The {@link #property-value} of this radio button group will be one of the keys in this object or `null`
   * if no radio button is checked.
   *
   * For example, consider the following configuration:
   * ```javascript
   *  {
   *      type    : 'radiogroup',
   *      name    : 'resolution',
   *      value   : 'A',
   *      options : {
   *          A : 'Keep the original version',
   *          B : 'Use the new version',
   *          C : 'Reconcile individual conflicts'
   *      }
   *  }
   * ```
   *
   * The above is equivalent to this configuration below using {@link #config-items}:
   * ```javascript
   *  {
   *      type  : 'radiogroup',
   *      items : [{
   *          text         : 'Keep the original version',
   *          name         : 'resolution',
   *          ref          : 'resolution_A',
   *          checked      : true,
   *          checkedValue : 'A'
   *      }, {
   *          text         : 'Use the new version',
   *          name         : 'resolution',
   *          ref          : 'resolution_B',
   *          checkedValue : 'B'
   *      }, {
   *          text         : 'Reconcile individual conflicts',
   *          name         : 'resolution',
   *          ref          : 'resolution_C',
   *          checkedValue : 'C'
   *      }]
   *  }
   * ```
   * @config {Object<String,String|RadioConfig>} options
   */
  options: {
    value: null,
    $config: {
      merge: "items"
    }
  },
  defaultBindProperty: "value"
});
RadioGroup.initClass();
RadioGroup._$name = "RadioGroup";

// ../Core/lib/Core/widget/CheckboxGroup.js
var CheckboxGroup = class extends RadioGroup.mixin(Validatable_default) {
  onItemBeforeChange(ev) {
    const me = this, { lastValue } = me;
    if (!me.reverting && me.trigger("beforeChange", me.wrapRadioEvent(ev)) === false) {
      if (lastValue != null && lastValue !== me.value) {
        me.reverting = true;
        ev.source.uncheckToggleGroupMembers();
        me.value = lastValue;
        me.lastValue = lastValue;
        me.reverting = false;
        return false;
      }
    }
  }
  onItemChange(ev) {
    const me = this;
    if (!me.reverting) {
      me.triggerFieldChange(me.wrapRadioEvent(ev));
      me.lastValue = me.value;
    }
    me.syncInvalid();
  }
  get selected() {
    return this.existingOptions.filter((c) => c.input.checked) || null;
  }
  /**
   * This property corresponds to the {@link Core.widget.Checkbox#config-checkedValue} of all the currently
   * {@link Core.widget.Checkbox#property-checked} checkboxes.
   *
   * Accepts a comma-separated string representing the checked values.
   * @prp {String[]}
   * @accepts {String|String[]}
   */
  get value() {
    const { selected } = this;
    return selected.map((widget) => widget.checkedValue);
  }
  set value(value2) {
    value2 = value2 || [];
    if (typeof value2 === "string") {
      value2 = value2.split(this.separator);
    }
    this.existingOptions.forEach((c) => {
      c.isConfiguring = this.isConfiguring;
      c.checked = value2.includes(c.checkedValue);
      c.isConfiguring = false;
    });
    if (BrowserHelper.isFirefox && !this.isConnected) {
      this.__value = value2;
    }
  }
  internalOnChange() {
    this.syncInvalid();
  }
  // region validation
  // CheckboxGroup has no wrapper like field does
  // so return element instead since error tooltip uses field.inputWrap
  get inputWrap() {
    return this.element;
  }
  get isValid() {
    let result = true;
    const me = this, { selected, requiredSelectedOptions, minSelectedOptions, maxSelectedOptions } = me;
    me.clearError(void 0, true);
    if (!me.disabled) {
      const minOk = isNaN(me.minSelectedOptions) || selected.length >= minSelectedOptions, maxOk = isNaN(me.maxSelectedOptions) || selected.length <= maxSelectedOptions;
      if (requiredSelectedOptions && selected.length !== requiredSelectedOptions) {
        result = false;
        me.setError(me.L("L{wrongNbrOfOptionsSelected}", requiredSelectedOptions), true);
      } else if (!minOk) {
        result = false;
        me.setError(me.L("L{tooFewOptionsSelected}", minSelectedOptions), true);
      } else if (!maxOk) {
        result = false;
        me.setError(me.L("L{tooManyOptionsSelected}", maxSelectedOptions), true);
      }
    }
    if (result && me.errors && Object.keys(me.errors).length) {
      result = false;
    }
    return result;
  }
  updateMinSelectedOptions() {
    if (!this.isConfiguring) {
      this.syncInvalid();
    }
  }
  updateMaxSelectedOptions() {
    if (!this.isConfiguring) {
      this.syncInvalid();
    }
  }
  updateRequiredSelectedOptions() {
    if (!this.isConfiguring) {
      this.syncInvalid();
    }
  }
  // endregion
};
//region Config
__publicField(CheckboxGroup, "$name", "CheckboxGroup");
__publicField(CheckboxGroup, "type", "checkboxgroup");
/**
 * Fired before one of the checkboxes is toggled, return `false` to prevent the action.
 * @event beforeChange
 * @param {String[]} value This widget's value
 * @param {Core.widget.CheckboxGroup} source This widget
 */
/**
 * Fired when one of the checkboxes is toggled.
 * @event change
 * @param {String[]} value This widget's value
 * @param {Core.widget.CheckboxGroup} source This widget
 */
__publicField(CheckboxGroup, "configurable", {
  defaultType: "checkbox",
  defaults: {
    color: "b-blue"
  },
  separator: ",",
  /**
   * @hideconfigs clearable
   */
  /**
   * The set of options for this checkbox group. The keys of this object hold the checkbox's
   * {@link Core.widget.Checkbox#config-checkedValue} while the object values are a string for the checkbox's
   * {@link Core.widget.Checkbox#config-text} or a config object for that checkbox.
   *
   * The {@link #property-value} of this checkbox group will be one of the keys in this object or `null`
   * if no checkbox is checked.
   *
   * Example:
   * ```javascript
   *  {
   *      type    : 'checkboxgroup',
   *      name    : 'resolution',
   *      value   : 'A',
   *      title   : 'Allergies',
   *      options : {
   *          A : 'Gluten',
   *          B : 'Bacon',
   *          C : 'Lactose',
   *          D : 'Fish'
   *      }
   *  }
   * ```
   * @config {Object<String,String|CheckboxConfig>} options
   */
  /**
   * The number of options the user is required to select for this field to be valid. See also
   * {@link #property-minSelectedOptions} and {@link #property-maxSelectedOptions} if you would like
   * to use a range.
   * @prp {Number}
   */
  requiredSelectedOptions: null,
  /**
   * The minimum number of options to select
   * @prp {Number}
   */
  minSelectedOptions: null,
  /**
   * The maximum number of options to select
   * @prp {Number}
   */
  maxSelectedOptions: null,
  errorTooltipCachePath: "checkboxGroupErrorTooltip",
  errorTooltipConfig: {
    forSelector: ".b-checkboxgroup.b-invalid .b-checkbox-label",
    align: "t-b"
  }
});
CheckboxGroup.initClass();
CheckboxGroup._$name = "CheckboxGroup";

// ../Core/lib/Core/widget/CodeEditor.js
var monacoLoadPromise;
var CodeEditor = class extends Panel {
  //endregion
  /**
   * Constructor for Code Editor.
   * @param config
   */
  construct(config = {}) {
    const me = this;
    me.editorReady = new Promise((resolve, reject) => {
      me.resolveEditorReady = resolve;
      me.rejectEditorReady = reject;
    });
    super.construct(...arguments);
    me.editorReady.then(() => {
      GlobalEvents_default.ion({
        theme: ({ theme }) => me.editor.updateOptions({ theme: theme.toLowerCase().includes("dark") ? "vs-dark" : "vs-light" }),
        thisObj: me
      });
      me.element.style.setProperty("--vscode-menu-background", getComputedStyle(me.element.querySelector(".monaco-editor")).getPropertyValue("--vscode-menu-background"));
    });
  }
  get monacoInstance() {
    return globalThis.monaco;
  }
  /**
   * Focuses the Monaco editor
   */
  focus() {
    this.editor.focus();
  }
  // If external code has "primed" the loading already, it can tell us by injecting a Promise
  // which yields the Monaco instance
  static set monacoLoadPromise(p) {
    monacoLoadPromise = p;
  }
  // Allow external code to see if loading has already begun
  static get monacoLoadPromise() {
    return monacoLoadPromise;
  }
  async updateCodePath(codePath, oldCodePath) {
    var _a5;
    const me = this;
    if (oldCodePath) {
      (_a5 = me.loaderScript) == null ? void 0 : _a5.remove();
    }
    if (!monacoLoadPromise) {
      monacoLoadPromise = new Promise((resolve) => {
        const onLoad = () => {
          const r = globalThis["require"];
          r.config({ paths: { vs: `${codePath}/min/vs` } });
          r(["vs/editor/editor.main"], function() {
            if (!me.isDestroyed) {
              resolve(globalThis.monaco);
            }
          });
          loader.removeEventListener("error", onError);
          loader.removeEventListener("load", onLoad);
        }, onError = () => {
          const msg = `Code editor path to Monaco editor ${codePath} is incorrect`;
          loader.removeEventListener("error", onError);
          loader.removeEventListener("load", onLoad);
          Toast.show(msg);
          me.rejectEditorReady(msg);
        }, loader = me.loaderScript = DomHelper.createElement({
          tag: "script",
          parent: document.head,
          src: `${codePath}/min/vs/loader.js`
        });
        loader.addEventListener("error", onError);
        loader.addEventListener("load", onLoad);
      });
    }
    globalThis.monaco = await monacoLoadPromise;
    me.getConfig("editor");
    me.getConfig("text");
  }
  updateReadOnly(readOnly) {
    const { readOnly: readOnlyIcon } = this.widgetMap;
    readOnlyIcon && (readOnlyIcon.hidden = !readOnly);
    this.editor.updateOptions({ readOnly });
  }
  updateStatus(status) {
    const { status: statusWidget } = this.widgetMap;
    statusWidget && (statusWidget.html = status);
  }
  changeEditor(editor, oldEditor) {
    var _a5, _b2;
    const me = this;
    (_a5 = oldEditor == null ? void 0 : oldEditor.dispose) == null ? void 0 : _a5.call(oldEditor);
    if (editor) {
      editor = me.monacoInstance.editor.create(me.contentElement, Config.merge({
        theme: me.theme || ((_b2 = DomHelper.themeInfo) == null ? void 0 : _b2.name.toLowerCase().includes("dark")) ? "vs-dark" : "vs-light",
        padding: {
          top: 14
        }
      }, editor));
      me.monacoInstance.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
        noSuggestionDiagnostics: true
      });
      editor.onDidChangeCursorPosition(me.onCursorMove.bind(me));
      editor.onDidChangeConfiguration(me.onEditorConfigChange.bind(me));
      editor.onDidDispose(me.onEditorDestroy.bind(me));
      me.resolveEditorReady(editor);
    }
    return editor;
  }
  get text() {
    var _a5;
    return ((_a5 = this.codeModel) == null ? void 0 : _a5.getValue()) || this._text;
  }
  changeText(text) {
    var _a5;
    this._text = (_a5 = this.codeModel) == null ? void 0 : _a5.getValue();
    return text;
  }
  updateText(text) {
    var _a5, _b2;
    const { codeModel } = this;
    if (text != null) {
      codeModel ? codeModel.setValue(text) : this.loadText(text);
    } else {
      (_b2 = (_a5 = this.codeModel) == null ? void 0 : _a5.dispose) == null ? void 0 : _b2.call(_a5);
    }
  }
  /**
   * Loads text into the editor.
   * @param {String} text The text to edit.
   * @param {String} language The language to edit the text with.
   * @returns {Object} The https://microsoft.github.io/monaco-editor/docs.html#interfaces/editor.ITextModel.html
   * which is handling editing the text.
   */
  async loadText(text = this.text, language = this.language) {
    var _a5;
    const me = this;
    me._text = text;
    me._language = language;
    (_a5 = me.codeModel) == null ? void 0 : _a5.dispose();
    await me.editorReady;
    const model = me.codeModel = me.monacoInstance.editor.createModel(text, language);
    me.editor.setModel(model);
    me.trigger("load", { model, text });
    return model;
  }
  onEditorDestroy() {
    var _a5, _b2;
    (_b2 = (_a5 = this.codeModel) == null ? void 0 : _a5.dispose) == null ? void 0 : _b2.call(_a5);
    this.codeModel = null;
  }
  onEditorConfigChange() {
    const me = this;
    if (me.editor) {
      me.readOnly = me.editor.getOptions().get(me.monacoInstance.editor.EditorOptions.readOnly.id);
    }
  }
  onCursorMove({ position }) {
    var _a5;
    (_a5 = this.widgetMap.cursorPos) == null ? void 0 : _a5.setPosition(position);
  }
};
//region Config
__publicField(CodeEditor, "$name", "CodeEditor");
__publicField(CodeEditor, "type", "codeeditor");
__publicField(CodeEditor, "configurable", {
  textContent: false,
  scrollable: null,
  /**
   * The path from which to load the Monaco editor.
   *
   * For example:
   *
   * ```javascript
   * {
   *   codePath : 'node_modules/monaco-editor'
   * }
   * ```
   *
   * @config {String}
   */
  codePath: null,
  /**
   * The [Monaco](https://microsoft.github.io/monaco-editor/) editor
   * instance. Use this property to manipulate the editor according to
   * https://microsoft.github.io/monaco-editor/typedoc/index.html
   *
   * Note that this will only exist when the editor has been loaded from the {@link #config-codePath}.
   *
   * This will be indicated by the {@link #property-editorReady} property which yields the
   * monaco editor as its value.
   * @member {Object} editor
   * @readonly
   */
  /**
   * An editor configuration object to be passed to https://microsoft.github.io/monaco-editor/docs.html#functions/editor.create.html
   * @config {Object}
   */
  editor: {
    $config: ["lazy", "nullify"],
    value: {
      automaticLayout: true,
      scrollBeyondLastLine: false
    }
  },
  header: {
    cls: "demo-header"
  },
  bbar: {
    overflow: null,
    items: {
      readOnly: {
        hidden: true,
        type: "widget",
        cls: "b-icon b-icon-locked"
      },
      status: {
        type: "widget",
        html: "\xA0"
      },
      cursorPos: {
        type: "widget",
        html: "\xA0",
        setPosition({ lineNumber, column }) {
          this.element.innerHTML = `Ln ${lineNumber}, Col ${column}`;
        }
      }
    }
  },
  /**
   * The read-only state of the editor may be set and read. This state is by default reflected
   * by an icon in the bottom toolbar.
   * @prp {Boolean}
   */
  readOnly: null,
  /**
   * The text being edited may be set and read using this property.
   * @prp {String}
   */
  text: {
    $config: ["lazy", "nullify"],
    value: null
  },
  /**
   * The language being edited may be set and read using this property.
   * @prp {String}
   */
  language: null,
  /**
   * The Monaco theme to use. If not specified, it defaults to `'vs'`;
   * @prp {'vs'|'vs-dark'|'hc-black'|'hc-light'}
   */
  theme: null,
  /**
   * The status message displayed un the bottom toolbar may be set and read using this property.
   * @prp {String}
   */
  status: null
});
CodeEditor.initClass();
CodeEditor._$name = "CodeEditor";

// ../Core/lib/Core/widget/ColorPicker.js
var ColorPicker = class extends List {
  configure(config) {
    super.configure(config);
    this.setItems();
    this.ion({
      item: "onColorSelect"
    });
  }
  setItems() {
    const me = this, { colors, colorClasses } = me;
    let useColors = colors || colorClasses;
    if (me.addNoColorItem) {
      me.store.modelClass.allowNullId = true;
      useColors = [...useColors, null];
    }
    me.items = useColors.map((color) => !color || typeof color == "string" ? { color, text: (colorClasses ? StringHelper.capitalize(color) : color) || me.L("L{noColor}") } : color);
  }
  afterConfigure() {
    var _a5;
    super.afterConfigure(...arguments);
    if (this.addNoColorItem || this.value) {
      this.select((_a5 = this.value) != null ? _a5 : null);
    }
  }
  updateColors() {
    if (!this.isConfiguring) {
      this.setItems();
    }
  }
  updateAddNoColorItem() {
    if (!this.isConfiguring) {
      this.setItems();
    }
  }
  onColorSelect({ record }) {
    var _a5;
    this.refresh();
    const event = { color: record.color, record, bubbles: true };
    (_a5 = this.colorSelected) == null ? void 0 : _a5.call(this, event);
    this.trigger("colorSelected", event);
  }
  select(value2) {
    if (!value2 || typeof value2 === "string") {
      value2 = this.store.findRecord("color", value2 != null ? value2 : null);
    }
    value2 && super.select(value2);
  }
  updateColumns(columns) {
    this.style = `grid-template-columns:repeat(${columns}, 1fr);`;
  }
  getColorClassName(color) {
    var _a5, _b2;
    if (((_a5 = this.colorClasses) == null ? void 0 : _a5.includes(color)) || ((_b2 = this.colorClasses) == null ? void 0 : _b2.find((r) => r && r.color === color))) {
      return this.colorClassPrefix + color;
    }
    return color ? "" : "b-no-color";
  }
};
__publicField(ColorPicker, "$name", "ColorPicker");
__publicField(ColorPicker, "type", "colorpicker");
__publicField(ColorPicker, "configurable", {
  itemsFocusable: false,
  navigator: {
    focusCls: "b-color-active"
  },
  itemWrapperTpl(record, i) {
    const { selected } = this, { color, text } = record, colorClassName = this.getColorClassName(color);
    return TemplateHelper.tpl`
                    <li
                        class="${this.getItemClasses(record, i)} ${colorClassName}"
                        role="option"
                        aria-selected="${selected.includes(record)}"
                        data-index="${i}"
                        data-id="${StringHelper.encodeHtml(color)}"
                        data-btip="${text}"
                        ${this.itemsFocusable ? 'tabindex="-1"' : ""}
                        style="${colorClassName ? "" : "background-color: " + color}"
                    ></li>`;
  },
  /**
   * Array of internal color class names, without prefix, like `red`, `violet` etc.
   * @config {String[]}
   * @private
   */
  colorClasses: null,
  /**
   * Prefix to be inserted before the color class names in {@link #config-colorClasses}, like `b-sch-`
   * @config {String}
   * @private
   */
  colorClassPrefix: null,
  /**
   * The `colors` property can be an array of string CSS colors or of objects with `color`
   * and `text` properties from which the user can chose from.
   *
   * Provide an array of string CSS colors:
   * ```javascript
   * new ColorMenu({
   *     colors : ['#00FFFF', '#F0FFFF', '#89CFF0', '#0000FF', '#7393B3']
   * });
   * ```
   *
   * The colors can also be named. To do that, put them in an object with a `color` and a `text` property, like:
   * ```javascript
   * new ColorMenu({
   *    colors : [
   *        { color : '#000000', text : 'Black'},
   *        { color : '#FF0000', text : 'Red'},
   *        { color : '#00FF00', text : 'Green'},
   *        { color : '#0000FF', text : 'Blue'},
   *        { color : '#FFFFFF', text : 'White'},
   *    ]
   * });
   * ```
   *
   * Default colors are:
   * <div class="b-colorbox b-inline" style="background-color: #45171D"></div>#45171D
   * <div class="b-colorbox b-inline" style="background-color: #F03861"></div>#F03861
   * <div class="b-colorbox b-inline" style="background-color: #FF847C"></div>#FF847C
   * <div class="b-colorbox b-inline" style="background-color: #FECEA8"></div>#FECEA8
   * <div class="b-colorbox b-inline" style="background-color: #A5F2E7"></div>#A5F2E7
   * <div class="b-colorbox b-inline" style="background-color: #AA83F3"></div>#AA83F3
   * <div class="b-colorbox b-inline" style="background-color: #8983F3"></div>#8983F3
   * <div class="b-colorbox b-inline" style="background-color: #A10054"></div>#A10054
   * <div class="b-colorbox b-inline" style="background-color: #073059"></div>#073059
   * <div class="b-colorbox b-inline" style="background-color: #2866AB"></div>#2866AB
   * <div class="b-colorbox b-inline" style="background-color: #5FBDC5"></div>#5FBDC5
   * <div class="b-colorbox b-inline" style="background-color: #D8D95C"></div>#D8D95C
   * <div class="b-colorbox b-inline" style="background-color: #FFDEDE"></div>#FFDEDE
   * <div class="b-colorbox b-inline" style="background-color: #F7F3CE"></div>#F7F3CE
   * <div class="b-colorbox b-inline" style="background-color: #C5ECBE"></div>#C5ECBE
   * <div class="b-colorbox b-inline" style="background-color: #3E3E3E"></div>#3E3E3E
   * <div class="b-colorbox b-inline" style="background-color: #405559"></div>#405559
   * <div class="b-colorbox b-inline" style="background-color: #68868C"></div>#68868C
   * <div class="b-colorbox b-inline" style="background-color: #EDEDED"></div>#EDEDED
   * <div class="b-colorbox b-inline" style="background-color: #D3D6DB"></div>#D3D6DB
   * <div class="b-colorbox b-inline" style="background-color: #3A4750"></div>#3A4750
   * <div class="b-colorbox b-inline" style="background-color: #303841"></div>#303841
   * <div class="b-colorbox b-inline" style="background-color: #BE3144"></div>#BE3144
   * @prp {Array<String|ColorDescriptor>}
   */
  colors: [
    "#45171D",
    "#F03861",
    "#FF847C",
    "#FECEA8",
    "#A5F2E7",
    "#AA83F3",
    "#8983F3",
    "#A10054",
    "#073059",
    "#2866AB",
    "#5FBDC5",
    "#D8D95C",
    "#FFDEDE",
    "#F7F3CE",
    "#C5ECBE",
    "#3E3E3E",
    "#405559",
    "#68868C",
    "#EDEDED",
    "#D3D6DB",
    "#3A4750",
    "#303841",
    "#BE3144"
  ],
  /**
   * Adds an option to set no background color
   * @prp {Boolean}
   */
  addNoColorItem: true,
  /**
   * The color items is displayed in a grid layout with 6 columns as default. Change this to another number to
   * affect appearance.
   * @prp {Number}
   */
  columns: 6,
  /**
   * A callback function that will be called when the user selects a color in the picker.
   * @param {Object} event Object containing event data
   * @param {Core.data.Model} event.record The selected color's record instance
   * @param {String} event.color The string color value
   * @config {Function}
   * @returns {void}
   */
  colorSelected: null,
  store: {
    // Don't pollute items
    useRawData: false
  }
});
ColorPicker.initClass();
ColorPicker._$name = "ColorPicker";

// ../Core/lib/Core/widget/ColorField.js
var ColorField = class extends Combo {
  configure(config) {
    var _a5;
    const pickerCfg = (_a5 = config.picker) != null ? _a5 : {};
    if (config.colors) {
      pickerCfg.colors = config.colors;
    }
    if ("addNoColorItem" in config) {
      pickerCfg.addNoColorItem = config.addNoColorItem;
    }
    config.picker = pickerCfg;
    super.configure(config);
  }
  updatePicker(picker, was) {
    super.updatePicker(picker, was);
    if (picker) {
      this.items = picker.store.records;
    }
  }
  updateColors(colors) {
    if (!this.isConfiguring) {
      this.picker.colors = colors;
    }
  }
  updateAddNoColorItem(addNoColorItem) {
    if (!this.isConfiguring) {
      this.picker.addNoColorItem = addNoColorItem;
    }
  }
  set value(value2) {
    if (!this.store) {
      this.items = [];
      this.store = this.picker.store;
    }
    if (!value2) {
      value2 = this.store.findRecord("color", null);
    }
    super.value = value2;
  }
  showPicker() {
    this.picker.refresh();
    super.showPicker(...arguments);
  }
  get value() {
    return super.value;
  }
  syncInputFieldValue(...args) {
    var _a5;
    const me = this, { value: value2 } = me;
    let className = (_a5 = me.picker) == null ? void 0 : _a5.getColorClassName(value2);
    if (!className) {
      me.colorBox.style.color = value2;
    }
    className = "b-colorbox " + className;
    me.colorBox.className = className;
    if (!me.showBoxForNoColor) {
      me.element.classList.toggle("b-colorless", !value2);
    }
    super.syncInputFieldValue(...args);
  }
  get innerElements() {
    return [
      {
        reference: "colorBox",
        className: "b-colorbox"
      },
      ...super.innerElements
    ];
  }
};
__publicField(ColorField, "$name", "ColorField");
__publicField(ColorField, "type", "colorfield");
__publicField(ColorField, "configurable", {
  /**
   * @hideconfigs text,color,editable,picker
   */
  displayField: "text",
  valueField: "color",
  editable: false,
  picker: {
    type: "colorpicker",
    align: {
      align: "t100-b100",
      matchSize: false
    }
  },
  showBoxForNoColor: true,
  /**
   * The `colors` property can be an array of string CSS colors or of objects with `color`
   * and `text` properties from which the user can chose from. This will override the
   * {@link Core.widget.ColorPicker#config-colors pickers default colors}.
   *
   * Provide an array of string CSS colors:
   * ```javascript
   * new ColorField({
   *     colors : ['#00FFFF', '#F0FFFF', '#89CFF0', '#0000FF', '#7393B3']
   * });
   * ```
   *
   * @prp {Array<String|ColorDescriptor>}
   */
  colors: null,
  /**
   * Adds an option in the picker to set no background color
   * @prp {Boolean}
   */
  addNoColorItem: true
});
ColorField.initClass();
ColorField._$name = "ColorField";

// ../Core/lib/Core/widget/ConfirmationBar.js
var choices = ["ok", "cancel", "yes", "no"].reduce((o, v, i, a) => {
  o[v] = Object.freeze({
    ...Object.fromEntries(a.map((k) => [k, false])),
    name: v,
    approve: "ok,yes".includes(v),
    [v]: true
  });
  return o;
}, /* @__PURE__ */ Object.create(null));
var defaultClasses = ["b-raised", "b-blue"];
var nonDefaultClasses = ["b-gray"];
var okFirst = BrowserHelper.isWindows;
var splitRe = /(?:\s+,?|,)\s*/;
var widthyConfigs = /^(icon|menu|text)$/;
var ConfirmationBar = class extends Toolbar {
  get buttons() {
    return this.items.filter((it) => it.isButton);
  }
  /**
   * Returns the `cancel` button if present in the {@link #config-items} config or `null` if not present.
   * @member {Core.widget.Button}
   * @readonly
   */
  get cancelButton() {
    return this.widgetMap.cancel || null;
  }
  /**
   * Returns the `no` button if present in the {@link #config-items} config or `null` if not present.
   * @member {Core.widget.Button}
   * @readonly
   */
  get noButton() {
    return this.widgetMap.no || null;
  }
  /**
   * Returns the `ok` button if present in the {@link #config-items} config or `null` if not present.
   * @member {Core.widget.Button}
   * @readonly
   */
  get okButton() {
    return this.widgetMap.ok || null;
  }
  /**
   * Returns the `yes` button if present in the {@link #config-items} config or `null` if not present.
   * @member {Core.widget.Button}
   * @readonly
   */
  get yesButton() {
    return this.widgetMap.yes || null;
  }
  changeItems(items2, was) {
    if (typeof items2 === "string") {
      items2 = Object.fromEntries(items2.split(splitRe).map((k) => [k, true]));
    }
    return super.changeItems(items2, was);
  }
  clickButton(event, cancel) {
    const me = this, { widgetMap, defaultButton } = me;
    let button = cancel ? widgetMap.cancel || widgetMap.no : widgetMap[defaultButton], btn, key;
    if (!button) {
      for ([key, btn] of Object.entries(widgetMap)) {
        if (key !== defaultButton) {
          button = btn;
          break;
        }
      }
    }
    button == null ? void 0 : button.onInternalClick(event);
  }
  /**
   * Called by the {@link #config-keyMap} to process `Escape`. This triggers a click from the first button in the
   * {@link #config-items} config that is present:
   *  - {@link #property-cancelButton}
   *  - {@link #property-noButton}
   *  - the first button that is not the {@link #config-defaultButton}
   * @param {Event} event The keyboard event
   * @internal
   */
  doCancel(event) {
    this.clickButton(event, true);
  }
  /**
   * Called by the {@link #config-keyMap} to process `Enter` or `NumpadEnter`. This triggers a click from the
   * {@link #config-defaultButton}.
   * @param {Event} event The keyboard event
   * @internal
   */
  doDefault(event) {
    this.clickButton(event);
  }
  onButtonClick(info) {
    this.trigger("choice", {
      ...info,
      choice: choices[info.source.ref],
      source: this,
      userAction: true
    });
  }
  onChildAdd(child) {
    super.onChildAdd(child);
    FunctionHelper.before(child, "onConfigChange", "onChildConfigChange", this);
    this.syncButtons();
  }
  onChildConfigChange({ name }) {
    widthyConfigs.test(name) && this.syncButtons();
  }
  onChildRemove(child) {
    super.onChildRemove(child);
    this.syncButtons();
  }
  onInternalPaint(info) {
    super.onInternalPaint(info);
    info.firstPaint && this.syncButtons.resume();
    this.syncButtons.now();
  }
  syncButtons() {
    const me = this, buttons = me.isDestroying ? [] : me.buttons, { defaultButton } = me;
    let minWidth, text;
    me.syncWidthButtons !== false && buttons.forEach((button) => {
      if (text = button.text) {
        minWidth = Math.max(minWidth || 0, text.length + Boolean(button.icon) + Boolean(button.menu));
      }
    });
    buttons.forEach((button) => {
      const { classList } = button.element, isDefault = button.ref === defaultButton;
      classList.add(...isDefault ? defaultClasses : nonDefaultClasses);
      classList.remove(...isDefault ? nonDefaultClasses : defaultClasses);
      classList.toggle("b-default-button", isDefault);
      button.minWidth = minWidth && button.text ? `${minWidth}em` : null;
    });
  }
  updateAutoWidthButtons() {
    this.syncButtons();
  }
  updateDefaultButton() {
    this.syncButtons();
  }
};
__publicField(ConfirmationBar, "$name", "ConfirmationBar");
__publicField(ConfirmationBar, "type", "confirmationbar");
// Factoryable type name
__publicField(ConfirmationBar, "configurable", {
  /**
   * The name of the default button. Must match with the defaults in {@link #config-namedItems}.
   * @prp {'cancel'|'ok'|'no'|'yes'}
   * @default
   */
  defaultButton: "ok",
  defaultType: "button",
  /**
   * In addition to normal form of {@link Core.widget.Container#config-items}, this class supports a comma-delimited
   * string.
   *
   * For example:
   * ```javascript
   *  items : 'ok,cancel',
   *  // or
   *  items : 'yes,no',
   * ```
   *
   * These are equivalent to:
   * ```javascript
   *  items : {
   *      ok     : true,
   *      cancel : true
   *  },
   *  // or
   *  items : {
   *      yes : true,
   *      no  : true
   *  },
   * ```
   * Both forms use the pre-defined buttons: `'cancel'`, `'ok'`, `'no'`, `'yes'`.
   *
   * @config {String|Object<String,ContainerItemConfig|MenuItemConfig|Boolean|null>|Array<ContainerItemConfig|MenuItemConfig|Core.widget.Widget>}
   * @default
   */
  items: "ok,cancel",
  /**
   * Maps `Enter` and `Escape` to {@link #function-doDefault} and {@link #function-doCancel}, respectively.
   * @config {Object<String,KeyMapConfig>}
   * @default
   */
  keyMap: {
    Enter: "doDefault",
    NumpadEnter: "doDefault",
    Escape: "doCancel"
  },
  /**
   * The default buttons available for use in the {@link Core.widget.Container#config-items} config.
   * @config {Object<string,ContainerItemConfig>}
   * @default
   */
  namedItems: {
    yes: {
      cls: "b-yes-button",
      text: "L{Object.Yes}",
      onClick: "up.onButtonClick",
      weight: okFirst ? 100 : 130
    },
    no: {
      cls: "b-no-button",
      text: "L{Object.No}",
      onClick: "up.onButtonClick",
      weight: okFirst ? 110 : 120
    },
    ok: {
      cls: "b-ok-button",
      text: "L{Object.Ok}",
      onClick: "up.onButtonClick",
      weight: okFirst ? 120 : 110
    },
    cancel: {
      cls: "b-cancel-button",
      text: "L{Object.Cancel}",
      onClick: "up.onButtonClick",
      weight: okFirst ? 130 : 100
    }
  },
  /**
   * Set to `false` to disable synchronizing button widths. By default, buttons are assigned a `minWidth` based
   * on the longest {@link Core.widget.Button#config-text}. The default OK and Cancel buttons (in the en-US locale)
   * will be assigned `minWidth = '6em'`.
   * @prp {Boolean}
   * @default true
   */
  syncWidthButtons: null
});
__publicField(ConfirmationBar, "delayable", {
  syncButtons: {
    type: "raf",
    suspended: true
    // suspended until initial paint
  }
});
ConfirmationBar.initClass();
ConfirmationBar._$name = "ConfirmationBar";

// ../Core/lib/Core/widget/MonthPicker.js
var MonthPicker = class extends Panel {
  construct(config) {
    super.construct({
      month: (/* @__PURE__ */ new Date()).getMonth(),
      ...config
    });
    EventHelper.on({
      element: this.contentElement,
      click: "onMonthClick",
      delegate: ".b-monthpicker-month",
      thisObj: this
    });
  }
  get focusElement() {
    return this.getMonthButton(this.month);
  }
  getMonthButton(month2) {
    return this.contentElement.querySelector(`.b-monthpicker-month[data-month="${month2}"]`);
  }
  onMonthClick({ target }) {
    const clickedMonth = parseInt(target.getAttribute("data-month"));
    if (this.month === clickedMonth) {
      this.trigger("select", { oldValue: clickedMonth, value: clickedMonth });
    } else {
      this.month = clickedMonth;
    }
  }
  handleTitleClick(event) {
    this.trigger("titleClick", event);
  }
  changeMonth(month2) {
    if (!isNaN(month2)) {
      return ObjectHelper.isDate(month2) ? month2.getMonth() : month2 >= 0 && month2 <= 11 ? month2 : void 0;
    }
  }
  updateMonth(month2, oldValue) {
    if (!this.isConfiguring) {
      this.trigger("select", { oldValue, value: month2 });
    }
  }
  composeBody() {
    this.getConfig("month");
    const result = super.composeBody(), children = result.children[this.tbar ? 1 : 0].children = [];
    this.widgetMap.title.text = DateHelper.getMonthName(this.month);
    for (let month2 = 0; month2 <= 11; month2++) {
      children.push({
        tag: "button",
        dataset: { month: month2 },
        class: {
          "b-monthpicker-month": 1,
          "b-selected": month2 === this.month
        },
        text: DateHelper.getMonthShortName(month2)
      });
    }
    return result;
  }
  async updateLocalization() {
    if (!this.isConfiguring) {
      await this.recompose();
    }
    super.updateLocalization();
  }
};
__publicField(MonthPicker, "$name", "MonthPicker");
__publicField(MonthPicker, "type", "monthpicker");
__publicField(MonthPicker, "configurable", {
  textContent: false,
  tbar: {
    overflow: null,
    items: {
      title: {
        type: "button",
        cls: "b-monthpicker-title",
        onAction: "up.handleTitleClick"
      }
    }
  },
  itemCls: "b-month-container",
  /**
   * The month to use as the selected month (zero-based, 0-11). Defaults to the current month.
   * @prp {Number} month
   **/
  month: null
});
MonthPicker.initClass();
MonthPicker._$name = "MonthPicker";

// ../Core/lib/Core/widget/YearPicker.js
var YearPicker = class extends Panel {
  construct(config) {
    super.construct({
      year: (/* @__PURE__ */ new Date()).getFullYear(),
      ...config
    });
    EventHelper.on({
      element: this.contentElement,
      click: "onYearClick",
      delegate: ".b-yearpicker-year",
      thisObj: this
    });
  }
  get focusElement() {
    return this.getYearButton(this.year) || this.getYearButton(this.startYear);
  }
  getYearButton(y) {
    return this.contentElement.querySelector(`.b-yearpicker-year[data-year="${y}"]`);
  }
  /**
   * The currently selected year.
   * @member {Number} value
   */
  get value() {
    return this.year;
  }
  set value(year) {
    this.year = year;
  }
  onYearClick({ target }) {
    const clickedYear = Math.min(Math.max(parseInt(target.innerText), this.minYear || 1), this.maxYear || 9999);
    if (this.year === clickedYear) {
      this.trigger("select", { oldValue: clickedYear, value: clickedYear });
    } else {
      this.year = clickedYear;
    }
  }
  handleTitleClick(e) {
    this.trigger("titleClick", e);
  }
  previous() {
    this.startYear = this.startYear - this.yearButtonCount;
  }
  next() {
    this.startYear = this.endYear + 1;
  }
  ingestYear(year) {
    if (!isNaN(year)) {
      return ObjectHelper.isDate(year) ? year.getFullYear() : year;
    }
  }
  changeYear(year) {
    if (year = this.ingestYear(year)) {
      return Math.min(Math.max(year, this.minYear || 1), this.maxYear || 9999);
    }
  }
  updateYear(year, oldValue) {
    const me = this;
    if (!me.startYear || year > me.endYear) {
      me.startYear = year;
    } else if (year < me.startYear) {
      me.startYear = year - (me.yearButtonCount - 1);
    }
    if (!me.isConfiguring) {
      me.trigger("select", { oldValue, value: year });
    }
  }
  /**
   * The ending year displayed in the widget.
   * @member {Number} endYear
   * @readonly
   */
  get endYear() {
    return this.startYear + this.yearButtonCount - 1;
  }
  changeStartYear(startYear) {
    if (startYear = this.ingestYear(startYear)) {
      startYear = this.minYear ? Math.max(startYear, this.minYear) : startYear;
      return this.maxYear ? Math.min(startYear, this.maxYear - (this.yearButtonCount - 1)) : startYear;
    }
  }
  async updateStartYear(startYear, oldStartYear) {
    if (this.isVisible) {
      DomHelper.slideIn(this.contentElement, Math.sign(startYear - oldStartYear));
    }
  }
  composeBody() {
    this.getConfig("year");
    const { startYear } = this, result = super.composeBody(), children = result.children[this.tbar ? 1 : 0].children = [];
    this.widgetMap.title.text = `${`000${startYear}`.slice(-4)} - ${`000${this.endYear}`.slice(-4)}`;
    for (let i = 0, y = startYear; i < this.yearButtonCount; i++, y++) {
      children.push({
        tag: "button",
        dataset: {
          year: y
        },
        class: {
          "b-yearpicker-year": 1,
          "b-selected": y === this.year
        },
        text: `000${y}`.slice(-4)
      });
    }
    return result;
  }
};
__publicField(YearPicker, "$name", "YearPicker");
__publicField(YearPicker, "type", "yearpicker");
__publicField(YearPicker, "configurable", {
  textContent: false,
  /**
   * The definition of the top toolbar which displays the title and "previous" and
   * "next" buttons.
   *
   * This contains the following predefined `items` which may be reconfigured by
   * application code:
   *
   * - `title` A widget which displays the visible year range. Weight 100.
   * - `previous` A button which navigates to the previous block. Weight 200.
   * - `next` A button which navigates to the next block. Weight 300.
   *
   * These may be reordered:
   *
   * ```javascript
   * new YearPicker({
   *     appendTo : targetElement,
   *     tbar     : {
   *         items : {
   *             // Move title to centre
   *             title : {
   *                 weight : 250
   *             }
   *         }
   *     },
   *     width    : '24em'
   * });
   * ```
   * @config {ToolbarConfig}
   */
  tbar: {
    overflow: null,
    items: {
      previous: {
        type: "tool",
        cls: "b-icon b-icon-previous",
        onAction: "up.previous",
        weight: 100
      },
      title: {
        type: "button",
        cls: "b-yearpicker-title",
        weight: 200,
        onAction: "up.handleTitleClick"
      },
      next: {
        type: "tool",
        cls: "b-icon b-icon-next",
        onAction: "up.next",
        weight: 300
      }
    }
  },
  itemCls: "b-year-container",
  /**
   * The number of clickable year buttons to display in the widget.
   *
   * It may be useful to change this if a non-standard shape or size is used.
   * @config {Number}
   * @default
   */
  yearButtonCount: 12,
  /**
   * The currently selected year.
   * @member {Number} year
   */
  /**
   * The year to use as the selected year. Defaults to the current year.
   * @config {Number}
   */
  year: null,
  /**
   * The lowest year to allow.
   * @config {Number}
   */
  minYear: null,
  /**
   * The highest year to allow.
   * @config {Number}
   */
  maxYear: null,
  /**
   * The starting year displayed in the widget.
   * @member {Number} startYear
   */
  /**
   * The year to show at the start of the widget
   * @config {Number}
   */
  startYear: null
});
YearPicker.initClass();
YearPicker._$name = "YearPicker";

// ../Core/lib/Core/widget/DisplayField.js
var DisplayField = class extends TextField {
  static get configurable() {
    return {
      readOnly: true,
      editable: false,
      cls: "b-display-field",
      /**
       * A template string used to render the value of this field. Please note you are responsible for encoding
       * any strings protecting against XSS.
       *
       * ```javascript
       * new DisplayField({
       *     appendTo : document.body,
       *     name     : 'age',
       *     label    : 'Age',
       *     template : data => `${data.value} years old`
       * })
       * ```
       * @config {Function}
       * @param {ContainerItemConfig} fieldConfig Configuration object for the field
       * @returns {DomConfig|String|null}
       */
      template: null,
      ariaElement: "displayElement"
    };
  }
  get focusElement() {
  }
  changeReadOnly() {
    return true;
  }
  changeEditable() {
    return false;
  }
  get inputElement() {
    return {
      tag: "span",
      id: `${this.id}-input`,
      reference: "displayElement",
      html: this.template ? this.template(this.value) : StringHelper.encodeHtml(this.value)
    };
  }
};
__publicField(DisplayField, "$name", "DisplayField");
__publicField(DisplayField, "type", "displayfield");
__publicField(DisplayField, "alias", "display");
DisplayField.initClass();
DisplayField._$name = "DisplayField";

// ../Core/lib/Core/widget/DatePicker.js
var dateSort = (lhs, rhs) => lhs.valueOf() - rhs.valueOf();
var { DateSet: DateSet2 } = DateHelper;
var emptyArray9 = Object.freeze([]);
var consecutiveDates = (selection) => selection.every((date2, i) => !i || !(DateHelper.add(selection[i - 1], 1, "d") - date2));
var weekRange = { magnitude: 1, unit: "week" };
var DatePicker = class extends CalendarPanel {
  static get delayable() {
    return {
      refresh: "raf"
    };
  }
  static get configurable() {
    return {
      /**
       * The date that the user has navigated to using the UI *prior* to setting the widget's
       * value by selecting. The initial default is today's date. Can also be supplied as a `YYYY-MM-DD` date
       * string.
       *
       * This may be changed using keyboard navigation. The {@link Core.widget.CalendarPanel#property-date} is set
       * by pressing `ENTER` when the desired date is reached.
       *
       * Programmatically setting the {@link Core.widget.CalendarPanel#config-date}, or using the UI to select the
       * date by clicking it also sets the `activeDate`
       * @config {Date|'today'|String}
       */
      activeDate: {
        $config: "date",
        value: "today"
      },
      focusable: true,
      textContent: false,
      tbar: {
        overflow: null,
        items: {
          prevYear: {
            cls: "b-icon b-icon-first b-datepicker-nav-button",
            onAction: "up.gotoPrevYear",
            tooltip: "L{DatePicker.gotoPrevYear}",
            weight: 100
          },
          prevMonth: {
            cls: "b-icon b-icon-previous b-datepicker-nav-button",
            onAction: "up.gotoPrevMonth",
            tooltip: "L{DatePicker.gotoPrevMonth}",
            weight: 200
          },
          fields: {
            type: "container",
            cls: "b-datepicker-title",
            items: {
              monthButton: {
                type: "button",
                cls: "b-datepicker-monthbutton",
                internalListeners: {
                  click: "up.onMonthPickerRequested"
                }
              },
              yearButton: {
                type: "button",
                cls: "b-datepicker-yearbutton",
                internalListeners: {
                  click: "up.onYearPickerRequested"
                }
              }
            },
            weight: 300
          },
          nextMonth: {
            cls: "b-icon b-icon-next b-datepicker-nav-button",
            onAction: "up.gotoNextMonth",
            tooltip: "L{DatePicker.gotoNextMonth}",
            weight: 400
          },
          nextYear: {
            cls: "b-icon b-icon-last b-datepicker-nav-button",
            onAction: "up.gotoNextYear",
            tooltip: "L{DatePicker.gotoNextYear}",
            weight: 500
          }
        }
      },
      monthPicker: {
        value: {
          type: "MonthPicker",
          trapFocus: true,
          positioned: true,
          hidden: true,
          internalListeners: {
            titleClick: "up.onMonthPickerTitleClick",
            select: "up.onMonthPicked",
            Escape: "up.onMonthPickerTitleClick"
          }
        },
        $config: "lazy"
      },
      yearPicker: {
        value: {
          type: "YearPicker",
          yearButtonCount: 16,
          trapFocus: true,
          positioned: true,
          hidden: true,
          internalListeners: {
            titleClick: "up.onYearPickerTitleClick",
            select: "up.onYearPicked"
          }
        },
        $config: "lazy"
      },
      /**
       * The initially selected date (or a `YYYY-MM-DD` date string).
       * @config {Date|String}
       */
      date: null,
      /**
       * The minimum selectable date. Selection of and navigation to dates prior
       * to this date will not be possible.
       * @config {Date}
       */
      minDate: {
        $config: "date",
        value: null
      },
      /**
       * The maximum selectable date. Selection of and navigation to dates after
       * this date will not be possible.
       * @config {Date}
       */
      maxDate: {
        $config: "date",
        value: null
      },
      /**
       * By default, disabled dates cannot be navigated to, and they are skipped over
       * during keyboard navigation. Configure this as `true` to enable navigation to
       * disabled dates.
       * @config {Boolean}
       * @default
       */
      focusDisabledDates: null,
      /**
       * Configure as `true` to enable selecting multiple discontiguous date ranges using
       * click and Shift+click to create ranges and Ctrl+click to select/deselect individual dates.
       *
       * Configure as `'simple'` to enable selecting a single date range by clicking a
       * start date followed by the end date. If you configure multiSelect `range` on touch-devices, `simple`
       * will be the mode used.
       *
       * Configure as `'range'` to enable selecting a single date range by selecting a
       * start and end date. Hold "SHIFT" button to select date range. Ctrl+click may add
       * or remove dates to/from either end of the range.
       * @config {Boolean|'range'|'simple'}
       * @default false
       */
      multiSelect: null,
      /**
       * If {@link #config-multiSelect} is configured as `true`, this is an array of dates
       * which are selected. There may be multiple, discontiguous date ranges.
       *
       * If {@link #config-multiSelect} is configured as `'range'`, this is a two element array
       * specifying the first and last selected dates in a range.
       *
       * You can also provide the selected dates as an array of `YYYY-MM-DD` date strings
       * @config {Date[]|String[]}
       */
      selection: {
        $config: {
          equal: (v1, v2) => v1 && v1.equals(v2)
        },
        value: null
      },
      /**
       * By default, the month and year are editable. Configure this as `false` to prevent that.
       * @config {Boolean}
       * @default true
       */
      editMonth: {
        $config: "lazy",
        value: null
      },
      /**
       * By default, the year is visible. Configure this as `false` to prevent that.
       * @config {Boolean}
       * @default true
       */
      includeYear: {
        $config: "lazy",
        value: null
      },
      /**
       * The {@link Core.helper.DateHelper} format string to format the day names.
       * @config {String}
       * @default
       */
      dayNameFormat: "dd",
      trapFocus: true,
      ariaElement: "weeksElement",
      focusDescendant: true,
      /**
       * By default, when the {@link #property-date} changes, the UI will only refresh
       * if it doesn't contain a cell for that date, so as to keep a stable UI when
       * navigating.
       *
       * Configure this as `true` to refresh the UI whenever the month changes, even if
       * the UI already shows that date.
       * @config {Boolean}
       * @internal
       */
      alwaysRefreshOnMonthChange: null,
      /**
       * Configure as `true` to add a highlighted border and background to the week row which
       * encapsulates the selected date.
       * @prp {Boolean}
       * @default false
       */
      highlightSelectedWeek: null
    };
  }
  static get prototypeProperties() {
    return {
      /**
       * The class name to add to the calendar cell whose date which is outside the
       * {@link #config-minDate}/{@link #config-maxDate} range.
       * @config {String}
       * @private
       */
      outOfRangeCls: "b-out-of-range",
      /**
       * The class name to add to the currently focused calendar cell.
       * @config {String}
       * @private
       */
      activeCls: "b-active-date",
      /**
       * The class name to add to selected calendar cells.
       * @config {String}
       * @private
       */
      selectedCls: "b-selected-date"
    };
  }
  // region Init
  construct(config) {
    const me = this;
    super.construct(config);
    const { widgetMap } = me;
    me.externalCellRenderer = me.cellRenderer;
    me.cellRenderer = me.internalCellRenderer;
    me.element.removeAttribute("tabindex");
    me.ariaElement.setAttribute("aria-activedescendant", `${me.id}-active-day`);
    widgetMap.fields && !me.ariaLabel && me.element.setAttribute("aria-labelledby", widgetMap.fields.id);
    EventHelper.on({
      element: me.weeksElement,
      click: {
        handler: "onCellClick",
        delegate: `.${me.dayCellCls}:not(.${me.disabledCls}):not(.${me.outOfRangeCls})`
      },
      mousedown: {
        handler: "onCellMousedown",
        delegate: `.${me.dayCellCls}`
      },
      thisObj: me
    });
    me.getConfig("editMonth");
    me.getConfig("includeYear");
    me.refresh.flush();
  }
  get labelledElement() {
    return this.element;
  }
  afterHide() {
    var _a5, _b2;
    (_a5 = this._monthPicker) == null ? void 0 : _a5.hide();
    (_b2 = this._yearPicker) == null ? void 0 : _b2.hide();
    super.afterHide(...arguments);
  }
  doDestroy() {
    var _a5, _b2;
    (_a5 = this.yearButton) == null ? void 0 : _a5.destroy();
    (_b2 = this.monthButton) == null ? void 0 : _b2.destroy();
    super.doDestroy();
  }
  // endregion
  get activeDayCell() {
    return this.weeksElement.querySelector(`#${this.id}-active-day`);
  }
  get focusElement() {
    return !this.disabled && this.activeDayCell || this.element;
  }
  onMonthDateChange(info) {
    const ret = super.onMonthDateChange(info);
    this.ariaElement.setAttribute("aria-label", DateHelper.format(this.date, "MMMM YYYY"));
    this.refresh.flush();
    return ret;
  }
  updateEditMonth(editMonth) {
    const { monthButton, yearButton } = this.widgetMap, disallow = editMonth === false;
    monthButton && (monthButton.readOnly = disallow);
    yearButton && (yearButton.disabled = disallow);
  }
  updateIncludeYear(includeYear) {
    const { yearButton } = this.widgetMap;
    yearButton && (yearButton.hidden = includeYear === false);
  }
  updateReadOnly(readOnly) {
    if (readOnly) {
      this.element.setAttribute("inert", true);
    } else {
      this.element.removeAttribute("inert");
    }
  }
  doRefresh(...args) {
    const me = this, {
      date: date2,
      widgetMap
    } = me, oldActiveCell = me.activeDayCell, activeDate = DateHelper.betweenLesser(me.activeDate, me.month.startDate, me.month.endDate) ? me.activeDate : me._activeDate = date2;
    me.getConfig("tbar");
    super.doRefresh(...args);
    widgetMap.monthButton && (widgetMap.monthButton.text = me.constructor._monthNames[date2.getMonth()]);
    widgetMap.yearButton && (widgetMap.yearButton.text = date2.getFullYear());
    const dateOfOldActiveCell = DateHelper.parseKey(oldActiveCell == null ? void 0 : oldActiveCell.dataset.date);
    if (activeDate - dateOfOldActiveCell) {
      me.syncActiveDate(activeDate, dateOfOldActiveCell);
    }
  }
  internalCellRenderer({ cell, date: date2 }) {
    const me = this, {
      externalCellRenderer
    } = me, isSelected = me.isSelectedDate(date2), cellClassList = me.getCellClassList(...arguments);
    if (isSelected) {
      if (me.multiSelect) {
        const isStart = !me.isSelectedDate(DateHelper.add(date2, -1, "d")), isEnd = !me.isSelectedDate(DateHelper.add(date2, 1, "d"));
        cellClassList["b-range-start"] = isStart;
        cellClassList["b-range-end"] = isEnd;
        cellClassList["b-in-range"] = !isStart && !isEnd;
      }
    }
    DomHelper.updateClassList(cell, cellClassList);
    cell.innerHTML = `<div class="b-datepicker-cell-inner">${date2.getDate()}</div>`;
    cell.setAttribute("role", "gridcell");
    cell.setAttribute("aria-label", DateHelper.format(date2, "dddd MMMM Do, YYYY"));
    DomHelper.toggleAttribute(cell, "aria-selected", isSelected);
    if (me.isActiveDate(date2)) {
      cell.id = `${me.id}-active-day`;
    } else {
      cell.removeAttribute("id");
    }
    if (externalCellRenderer) {
      arguments[0].cell = cell.firstChild;
      me.callback(externalCellRenderer, this, arguments);
    }
  }
  getCellClassList({ date: date2 }) {
    const me = this, {
      activeCls,
      selectedCls
    } = me;
    return {
      [activeCls]: activeCls && me.isActiveDate(date2),
      [selectedCls]: me.isSelectedDate(date2),
      [me.outOfRangeCls]: me.minDate && date2 < me.minDate || me.maxDate && date2 > me.maxDate
    };
  }
  onCellMousedown(event) {
    const cell = event.target.closest("[data-date]");
    if (this.focusable) {
      cell.focus();
      if (DomHelper.getActiveElement(cell) === cell) {
        event.preventDefault();
      }
    } else {
      event.preventDefault();
    }
    this.activeDate = DateHelper.parseKey(cell.dataset.date);
  }
  onCellClick(event) {
    const cell = event.target.closest("[data-date]");
    this.onUIDateSelect(DateHelper.parseKey(cell.dataset.date), event);
  }
  /**
   * Called when the user uses the UI to select the current activeDate. So ENTER when focused
   * or clicking a date cell.
   * @param {Date} date The active date to select
   * @param {Event} event the instigating event, either a `click` event or a `keydown` event.
   * @internal
   */
  onUIDateSelect(date2, event) {
    const me = this, {
      lastClickedDate,
      multiSelect
    } = me;
    if (!me.isDisabledDate(date2)) {
      if (me.trigger("beforeDateSelect", { date: date2, event }) !== false) {
        me.lastClickedDate = date2;
        me.activatingEvent = event;
        if (multiSelect) {
          me.handleMultiSelect(lastClickedDate, date2, event);
        } else {
          me.date = me.selection = date2;
          if (me.floating) {
            me.hide();
          }
        }
        me.activatingEvent = null;
      }
    }
  }
  // Calls updateSelection if the selection is mutated
  handleMultiSelect(lastClickedDate, date2, event) {
    const me = this, {
      multiSelect
    } = me, _selection = me._selection || (me._selection = new DateSet2()), selection = _selection.dates, singleRange = multiSelect === "range" || multiSelect === "simple", isSimpleMultiSelect = multiSelect === "simple" || DomHelper.isTouchEvent, {
      size,
      generation
    } = _selection, rangeEnds = size && {
      [DateHelper.makeKey(DateHelper.add(selection[0], -1, "d"))]: 1,
      [DateHelper.makeKey(selection[0])]: 1,
      [DateHelper.makeKey(selection[selection.length - 1])]: 1,
      [DateHelper.makeKey(DateHelper.add(selection[selection.length - 1], 1, "d"))]: 1
    }, isSelected = _selection.has(date2), toggleFn = isSelected ? "delete" : "add";
    const clickedRangeEnd = singleRange && (rangeEnds == null ? void 0 : rangeEnds[DateHelper.makeKey(date2)]);
    if (isSimpleMultiSelect && size > 1) {
      _selection.clear();
      _selection.add(date2);
    } else {
      if (event.ctrlKey) {
        if (multiSelect === true || !size || clickedRangeEnd) {
          _selection[toggleFn](date2);
          if (singleRange && !_selection.has(me.rangeStartDate)) {
            me.rangeStartDate.setDate(me.rangeStartDate.getDate() + (date2 < selection[1] ? 1 : -1));
          }
        }
      } else if ((isSimpleMultiSelect || event.shiftKey) && size) {
        const [start, end] = [
          new Date(singleRange ? me.rangeStartDate || (me.rangeStartDate = selection[0]) : lastClickedDate),
          date2
        ].sort(dateSort);
        if (singleRange) {
          _selection.clear();
        }
        for (const d = start; d <= end; d.setDate(d.getDate() + 1)) {
          _selection.add(d);
        }
      } else if (!(_selection.has(date2) && _selection.size === 1)) {
        _selection.clear();
        _selection.add(date2);
      }
    }
    const newSize = _selection.size;
    if (newSize === 1) {
      me.rangeStartDate = date2;
    } else if (!newSize) {
      me.rangeStartDate = null;
    }
    if (_selection.generation !== generation) {
      me.updateSelection(_selection);
    }
  }
  updateHighlightSelectedWeek(highlightSelectedWeek) {
    this.element.classList.toggle("b-highlight-selected-week", Boolean(highlightSelectedWeek));
  }
  updateMultiSelect(multiSelect) {
    this.element.classList.toggle("b-multiselect", Boolean(multiSelect));
    if (!multiSelect) {
      this.selection = [...this.selection][0];
    }
  }
  /**
   * The selected Date(s).
   *
   * When {@link #config-multiSelect} is `'range'`, then this yields a two element array
   * representing the start and end of the selected range.
   *
   * When {@link #config-multiSelect} is `true`, this yields an array containing every selected
   * Date.
   * @member {Date[]} selection
   */
  get selection() {
    const { _selection } = this, dates = _selection ? _selection.dates : emptyArray9;
    return this.multiSelect === "range" && dates.length ? [dates[0], dates[dates.length - 1]] : dates;
  }
  getRange(snapToWeeks) {
    if (this.multiSelect) {
      const { selection } = this, startDate = selection[0], endDate = selection[selection.length - 1], endDatePlusOne = DateHelper.add(endDate, 1, "d");
      if (selection.length > 1 && endDatePlusOne.getDate() === startDate.getDate()) {
        return {
          unit: "month",
          magnitude: DateHelper.diff(startDate, endDatePlusOne, "month"),
          wholeMonth: startDate.getDate() === 1 && endDate.getDate() === DateHelper.getLastDateOfMonth(endDate).getDate() && consecutiveDates(selection),
          startDate,
          endDate
        };
      }
      if (startDate.getDay() == DateHelper.weekStartDay && endDate.getDay() === (DateHelper.weekStartDay + 6) % 7) {
        return {
          unit: "week",
          magnitude: DateHelper.diff(startDate, endDatePlusOne, "week"),
          startDate,
          endDate
        };
      }
      if (snapToWeeks) {
        const snappedStartDate = DateHelper.floor(startDate, weekRange), snappedEndDate = DateHelper.ceil(endDatePlusOne, weekRange), weekSpan = Math.max(Math.ceil(DateHelper.diff(snappedStartDate, snappedEndDate, "w")), 1);
        snappedEndDate.setDate(snappedEndDate.getDate() - 1);
        return {
          unit: "week",
          magnitude: weekSpan,
          startDate: snappedStartDate,
          endDate: snappedEndDate
        };
      }
      return {
        unit: "day",
        magnitude: DateHelper.diff(startDate, endDate, "d") + 1,
        startDate,
        endDate
      };
    }
  }
  get snappedRange() {
  }
  changeSelection(selection, was) {
    const me = this;
    let result, rangeStartDate;
    if (!selection) {
      result = new DateSet2();
    } else if (selection.isDateSet) {
      if (was === selection || (was == null ? void 0 : was.equals(selection))) {
        return was;
      }
      rangeStartDate = ObjectHelper.first(selection);
      result = selection;
    } else {
      if (!selection.forEach) {
        selection = [selection];
      }
      selection.forEach((d, i) => selection[i] = me.changeDate(d));
      rangeStartDate = selection[0];
      selection.sort(dateSort);
      if (me.multiSelect === "range" && selection.length === 2) {
        result = new DateSet2();
        for (const d = new Date(selection[0]); d <= selection[1]; d.setDate(d.getDate() + 1)) {
          result.add(d);
        }
      } else {
        rangeStartDate = selection[0];
        result = new DateSet2(selection);
      }
    }
    if (rangeStartDate) {
      me.rangeStartDate = DateHelper.clearTime(rangeStartDate);
    }
    return result;
  }
  updateSelection(dateSet, oldDateSet) {
    const me = this, { multiSelect } = me, { dates } = dateSet, selection = multiSelect === "range" ? [dates[0], dates[dates.length - 1]] : dates;
    if (!me.isConfiguring) {
      if (!multiSelect && dates.length && !me.peekConfig("date") && dates[0].getMonth() === me.month.month) {
        me.date = dates[0];
      }
      me.refresh.now();
      const event = {
        selection,
        userAction: Boolean(me.activatingEvent)
      };
      if (oldDateSet) {
        const { dates: dates2 } = oldDateSet;
        event.oldSelection = multiSelect === "range" ? [dates2[0], dates2[dates2.length - 1]] : dates2;
      }
      me.trigger("selectionChange", event);
    }
  }
  /**
   * Checks whether a date is selected.
   * @param {Date} date The date
   * @returns {Boolean}
   */
  isSelected(date2) {
    return this.selection.some((d) => DateHelper.isSameDate(d, date2));
  }
  onInternalKeyDown(keyEvent) {
    const me = this, keyName = keyEvent.key.trim() || keyEvent.code, activeDate = me.activeDate;
    let newDate = new Date(activeDate);
    if (keyName === "Escape") {
      if (me.monthPicker.isVisible) {
        return me.monthPicker.hide();
      }
      if (me.yearPicker.isVisible) {
        return me.yearPicker.hide();
      }
      if (me.floating) {
        return me.hide();
      }
    }
    if (activeDate && me.weeksElement.contains(keyEvent.target)) {
      do {
        switch (keyName) {
          case "ArrowLeft":
            keyEvent.preventDefault();
            if (keyEvent.ctrlKey) {
              newDate = me.gotoPrevMonth();
            } else {
              newDate.setDate(newDate.getDate() - 1);
            }
            break;
          case "ArrowUp":
            keyEvent.preventDefault();
            newDate.setDate(newDate.getDate() - 7);
            break;
          case "ArrowRight":
            keyEvent.preventDefault();
            if (keyEvent.ctrlKey) {
              newDate = me.gotoNextMonth();
            } else {
              newDate.setDate(newDate.getDate() + 1);
            }
            break;
          case "ArrowDown":
            keyEvent.preventDefault();
            newDate.setDate(newDate.getDate() + 7);
            break;
          case "Enter":
            return me.onUIDateSelect(activeDate, keyEvent);
        }
      } while (me.isDisabledDate(newDate) && !me.focusDisabledDates);
      if (me.minDate && newDate < me.minDate) {
        return;
      }
      if (me.maxDate && newDate > me.maxDate) {
        return;
      }
      me.activeDate = newDate;
    }
  }
  changeMinDate(minDate) {
    return minDate && CalendarPanel.prototype.changeDate.apply(this, arguments);
  }
  updateMinDate(minDate) {
    this._yearPicker && (this._yearPicker.minYear = minDate == null ? void 0 : minDate.getFullYear());
    this.refresh();
  }
  changeMaxDate(minDate) {
    return minDate && CalendarPanel.prototype.changeDate.apply(this, arguments);
  }
  updateMaxDate(maxDate) {
    this._yearPicker && (this._yearPicker.maxYear = maxDate == null ? void 0 : maxDate.getFullYear());
    this.refresh();
  }
  changeDate(date2) {
    return DateHelper.clamp(super.changeDate(date2), this.minDate, this.maxDate);
  }
  updateDate(date2, oldDate) {
    const me = this, { month: month2 } = me;
    if (me.isConfiguring && !me.initializingActiveDate) {
      if (!("selection" in me.initialConfig)) {
        me.selection = date2;
      }
      if (!("activeDate" in me.initialConfig)) {
        me.activeDate = date2;
      }
    }
    if (!month2.date || date2.getMonth() === month2.month || !me.getCell(date2) || me.alwaysRefreshOnMonthChange || me.isNavigating) {
      super.updateDate(date2);
    } else {
      const newMonth = month2.getOtherMonth(date2), changes = me.eventListeners && (oldDate ? date2.getDate() !== oldDate.getDate() | (newMonth.getWeekId(date2) !== month2.getWeekId(oldDate)) << 1 | (date2.getMonth() !== (oldDate == null ? void 0 : oldDate.getMonth())) << 2 | (date2.getFullYear() !== (oldDate == null ? void 0 : oldDate.getFullYear())) << 3 : 15);
      me.trigger("dateChange", {
        changes: {
          d: true,
          w: Boolean(changes & 2),
          m: Boolean(changes & 12),
          y: Boolean(changes & 8),
          r: newMonth.weekCount !== month2.weekCount
        },
        value: date2,
        oldValue: oldDate
      });
    }
  }
  changeActiveDate(activeDate, oldActiveDate) {
    const me = this;
    if (activeDate === "today") {
      activeDate = /* @__PURE__ */ new Date();
    }
    if (me.trigger("beforeActiveDateChange", { activeDate, oldActiveDate }) === false) {
      return;
    }
    activeDate = activeDate ? me.changeDate(activeDate) : me.date || (me.date = DateHelper.clearTime(/* @__PURE__ */ new Date()));
    if (isNaN(activeDate)) {
      throw new Error("DatePicker activeDate must be passed a Date, or a YYYY-MM-DD date string");
    }
    return DateHelper.clamp(activeDate, me.minDate, me.maxDate);
  }
  updateActiveDate(activeDate, wasActiveDate) {
    const me = this, { isConfiguring } = me;
    if (isConfiguring || !me.getCell(activeDate)) {
      me.initializingActiveDate = isConfiguring;
      me.date = activeDate;
      me.initializingActiveDate = false;
    }
    if (!isConfiguring && !me.refresh.isPending) {
      me.syncActiveDate(activeDate, wasActiveDate);
    }
  }
  syncActiveDate(activeDate, wasActiveDate) {
    const me = this, { activeCls } = me, activeCell = me.getCell(activeDate), wasActiveCell = wasActiveDate && me.getCell(wasActiveDate), activeElement = DomHelper.getActiveElement(me.element);
    if (activeCell) {
      me.focusable && activeCell.setAttribute("tabIndex", "0");
      activeCls && activeCell.classList.add(activeCls);
      activeCell.id = `${me.id}-active-day`;
      if (me.weeksElement.contains(activeElement)) {
        activeCell.focus();
      }
    }
    if (wasActiveCell && wasActiveCell !== activeCell) {
      me.focusable && wasActiveCell.removeAttribute("tabIndex");
      activeCls && wasActiveCell.classList.remove(activeCls);
      wasActiveCell.removeAttribute("id");
    }
  }
  set value(value2) {
    const me = this, {
      selection,
      duration
    } = me;
    me.isNavigating = true;
    if (value2) {
      value2 = me.changeDate(value2, me.value);
      if (me.multiSelect === "range" && (selection == null ? void 0 : selection.length) === 2) {
        if (!DateHelper.betweenLesserEqual(value2, ...selection)) {
          if (value2 < selection[0]) {
            me.selection = [value2, DateHelper.add(value2, duration - 1, "d")];
          } else {
            me.selection = [DateHelper.add(value2, -(duration - 1), "d"), value2];
          }
        }
        me.date = me.activeDate = value2;
        return;
      }
      if (value2 !== void 0) {
        me.selection = value2;
        me.date = me.activeDate = value2;
      }
    } else {
      me.date = me.activeDate = /* @__PURE__ */ new Date();
      me.selection = null;
    }
    me.isNavigating = false;
  }
  get value() {
    const { selection } = this, n = (selection == null ? void 0 : selection.length) || null;
    return n && selection[n - 1];
  }
  get duration() {
    return this.multiSelect === "range" ? DateHelper.diff(...this.selection, "d") + 1 : 1;
  }
  gotoPrevYear() {
    return this.goto(-1, "year");
  }
  gotoPrevMonth() {
    return this.goto(-1, "month");
  }
  gotoNextMonth() {
    return this.goto(1, "month");
  }
  gotoNextYear() {
    return this.goto(1, "year");
  }
  goto(direction, unit) {
    const me = this, { activeDate } = me, activeCell = activeDate && me.getCell(activeDate);
    let newDate;
    if (unit === "month" && activeCell && (activeDate == null ? void 0 : activeDate.getMonth()) === me.month.month + direction) {
      newDate = activeDate;
    } else {
      newDate = DateHelper.add(activeCell ? activeDate : me.date, direction, unit);
    }
    return me.gotoDate(newDate, direction, activeCell);
  }
  gotoDate(newDate, direction, activeCell = this.activeDate && this.getCell(this.activeDate)) {
    const me = this, firstDateOfNewMonth = new Date(newDate);
    firstDateOfNewMonth.setDate(1);
    const lastDateOfNewMonth = DateHelper.add(DateHelper.add(firstDateOfNewMonth, 1, "month"), -1, "day");
    if (me.minDate && direction < 0 && lastDateOfNewMonth < me.minDate || me.maxDate && direction > 0 && firstDateOfNewMonth > me.maxDate) {
      return;
    }
    me.isNavigating = true;
    const result = me.date = newDate;
    me.widgetMap.monthButton.text = me.constructor._monthNames[newDate.getMonth()];
    if (activeCell) {
      me.activeDate = newDate;
    }
    me.isNavigating = false;
    return result;
  }
  isActiveDate(date2) {
    return !(date2 - this.activeDate);
  }
  isSelectedDate(date2) {
    var _a5;
    return (_a5 = this._selection) == null ? void 0 : _a5.has(date2);
  }
  onFocusIn(ev) {
    var _a5;
    super.onFocusIn(ev);
    if (ev.toWidget === this && ev._target === this.element && !this.disabled) {
      (_a5 = this.focusElement) == null ? void 0 : _a5.focus();
    }
  }
  onMonthPickerRequested() {
    const { monthPicker } = this;
    if (monthPicker.isVisible) {
      monthPicker.hide();
    } else {
      monthPicker.month = this.activeDate.getMonth();
      monthPicker.show();
      monthPicker.focus();
    }
  }
  onYearPickerRequested() {
    const { yearPicker } = this;
    if (yearPicker.isVisible) {
      yearPicker.hide();
    } else {
      yearPicker.year = yearPicker.startYear = this.activeDate.getFullYear();
      yearPicker.show();
      yearPicker.focus();
    }
  }
  onMonthPickerTitleClick() {
    this.monthPicker.hide();
  }
  onYearPickerTitleClick() {
    this.yearPicker.hide();
  }
  onMonthPicked({ value: value2, source }) {
    var _a5;
    const me = this;
    me.gotoDate(DateHelper.add(me.activeDate, value2 - me.activeDate.getMonth(), "month"));
    me.widgetMap.monthButton.text = me.constructor._monthNames[me.activeDate.getMonth()];
    (_a5 = me.focusElement) == null ? void 0 : _a5.focus({ preventScroll: true });
    source.hide();
  }
  onYearPicked({ value: value2, source }) {
    var _a5;
    const newDate = new Date(this.activeDate);
    newDate.setFullYear(value2);
    this.activeDate = newDate;
    (_a5 = this.focusElement) == null ? void 0 : _a5.focus({ preventScroll: true });
    source.hide();
  }
  changeMonthPicker(monthPicker, oldMonthPicker) {
    return MonthPicker.reconfigure(oldMonthPicker, monthPicker ? MonthPicker.mergeConfigs({
      owner: this,
      appendTo: this.element
    }, monthPicker) : null, this);
  }
  changeYearPicker(yearPicker, oldYearPicker) {
    var _a5, _b2;
    return YearPicker.reconfigure(oldYearPicker, yearPicker ? YearPicker.mergeConfigs({
      owner: this,
      appendTo: this.element,
      minYear: (_a5 = this.minDate) == null ? void 0 : _a5.getFullYear(),
      maxYear: (_b2 = this.maxDate) == null ? void 0 : _b2.getFullYear()
    }, yearPicker) : null, this);
  }
  get childItems() {
    const { _yearPicker, _monthPicker } = this, result = super.childItems;
    if (_yearPicker) {
      result.push(_yearPicker);
    }
    if (_monthPicker) {
      result.push(_monthPicker);
    }
    return result;
  }
  updateLocalization() {
    var _a5;
    const me = this, { monthButton } = me.widgetMap, monthNames = me.constructor._monthNames = DateHelper.getMonthNames();
    if (monthButton && !me.isConfiguring) {
      monthButton.text = monthNames[(_a5 = me.monthPicker.month) != null ? _a5 : me.date.getMonth()];
    }
    super.updateLocalization();
  }
  changeMultiSelect(value2) {
    if (value2 === "range" && BrowserHelper.isTouchDevice) {
      value2 = "simple";
    }
    return value2;
  }
};
__publicField(DatePicker, "$name", "DatePicker");
__publicField(DatePicker, "type", "datepicker");
__publicField(DatePicker, "_monthNames", DateHelper.getMonthNames());
DatePicker.initClass();
DatePicker._$name = "DatePicker";

// ../Core/lib/Core/widget/DateField.js
var TODAY = /* @__PURE__ */ new Date();
var DateField = class extends PickerField {
  //endregion
  //region Events
  /**
   * Fired when this field's value changes.
   * @event change
   * @param {Core.widget.DateField} source - This DateField
   * @param {Date} value - This field's value
   * @param {Date} oldValue - This field's previous value
   * @param {Boolean} valid - True if this field is in a valid state
   * @param {Event} event - The triggering DOM event if any
   * @param {Boolean} userAction - Triggered by user taking an action (`true`) or by setting a value (`false`)
   *
   * @typings checked -> {Boolean} // Used for compatibility with Checkbox event
   */
  //endregion
  //region Init & destroy
  /**
   * Creates default picker widget
   *
   * @internal
   */
  changePicker(picker, oldPicker) {
    var _a5;
    if (picker == null ? void 0 : picker.isDatePicker) {
      return picker;
    }
    const me = this, config = DatePicker.mergeConfigs({
      owner: me,
      forElement: me.pickerAlignElement,
      minDate: me.min,
      maxDate: me.max,
      weekStartDay: me._weekStartDay,
      // need to pass the raw value to let the component to use its default value
      align: {
        anchor: me.overlayAnchor,
        target: me.pickerAlignElement
      },
      internalListeners: {
        selectionChange: "onPickerSelectionChange",
        thisObj: me
      }
    }, picker);
    if (me.calendarContainerCls) {
      config.cls = me.calendarContainerCls;
    }
    if (me.value) {
      config[me.isValid ? "value" : "activeDate"] = me.value;
    } else if (!config.activeDate) {
      config.activeDate = /* @__PURE__ */ new Date();
    }
    const result = DatePicker.reconfigure(oldPicker, picker ? config : null, me);
    (_a5 = result == null ? void 0 : result.refresh) == null ? void 0 : _a5.flush();
    return result;
  }
  //endregion
  //region Click listeners
  onPickerSelectionChange({ selection, source: picker }) {
    if (picker.isVisible) {
      const me = this, newValue = selection.length ? me.value ? DateHelper.copyTimeValues(new Date(selection[0]), me.value) : new Date(selection[0]) : null;
      me._isUserAction = me._isPickerInput = true;
      me.value = newValue;
      me._isPickerInput = me._isUserAction = false;
      picker.hide();
    }
  }
  getStepDate(direction) {
    const { step } = this;
    return DateHelper.add(this.value, (direction || 1) * step.magnitude, step.unit);
  }
  stepDate(direction, userAction = true) {
    const me = this;
    if (!me.readOnly && me.value) {
      const newValue = me.getStepDate(direction);
      if (!me.checkMinMax(newValue)) {
        if (userAction) {
          me._isUserAction = true;
          me.value = newValue;
          me.autoSelect && me.select();
          me._isUserAction = false;
        } else {
          me.value = newValue;
        }
      }
    }
  }
  onBackClick() {
    this.stepDate(-1);
  }
  onForwardClick() {
    this.stepDate(1);
  }
  //endregion
  //region Toggle picker
  showPicker(focusPicker) {
    if (this.readOnly) {
      return;
    }
    const me = this, { _picker } = me;
    if (_picker) {
      const pickerConfig = {
        minDate: me.min,
        maxDate: me.max
      };
      if (me.value) {
        pickerConfig[me.isValid ? "value" : "activeDate"] = me.value;
      } else if (!_picker.activeDate) {
        pickerConfig.activeDate = /* @__PURE__ */ new Date();
      }
      _picker.setConfig(pickerConfig);
    }
    super.showPicker(focusPicker);
  }
  focusPicker() {
    var _a5;
    (_a5 = this.picker) == null ? void 0 : _a5.focus();
  }
  //endregion
  // region Validation
  checkMinMax(value2) {
    const { min: min4, max: max3 } = this;
    return min4 && value2 < min4 ? -1 : max3 && value2 > max3 ? 1 : 0;
  }
  get isValid() {
    const me = this;
    me.clearError("L{Field.minimumValueViolation}", true);
    me.clearError("L{Field.maximumValueViolation}", true);
    let { value: value2 } = me;
    if (value2) {
      let { min: min4, max: max3, validateDateOnly } = me;
      if (validateDateOnly) {
        value2 = DateHelper.clearTime(value2, false);
        if (min4) {
          min4 = DateHelper.clearTime(min4, false);
        }
        if (max3) {
          max3 = DateHelper.clearTime(max3, false);
        }
      }
      if (min4 && value2 < min4) {
        me.setError("L{Field.minimumValueViolation}", true);
        return false;
      }
      if (max3 && value2 > max3) {
        me.setError("L{Field.maximumValueViolation}", true);
        return false;
      }
    }
    return super.isValid;
  }
  //endregion
  //region Getters/setters
  transformDateValue(value2, checkBounds = true) {
    const me = this;
    if (value2 != null) {
      if (!DateHelper.isDate(value2)) {
        if (typeof value2 === "string") {
          value2 = DateHelper.parse(value2, me.format, me.strictParsing) || DateHelper.parse(value2, me.fallbackFormat, me.strictParsing);
        } else {
          value2 = new Date(value2);
        }
      }
      if (DateHelper.isValidDate(value2)) {
        if (checkBounds && (!me.min || value2 - me.min > -DateHelper.MS_PER_DAY) && (!me.max || value2 <= me.max)) {
          return me.transformTimeValue(value2);
        }
        return value2;
      }
    }
    return null;
  }
  isTimeValue(value2) {
    return value2 && value2 !== true && value2 !== "entered";
  }
  transformTimeValue(value2) {
    const me = this, { keepTime } = me;
    value2 = DateHelper.clone(value2);
    if (!keepTime) {
      DateHelper.clearTime(value2, false);
    } else if (me.isTimeValue(keepTime)) {
      const timeValue = DateHelper.parse(keepTime, me.timeFormat);
      DateHelper.copyTimeValues(value2, timeValue);
    } else if (keepTime === true && (me._isPickerInput || me.inputting) && DateHelper.isValidDate(me.value)) {
      DateHelper.copyTimeValues(value2, me.value);
    }
    return value2;
  }
  changeMin(value2) {
    return this.transformDateValue(value2, false);
  }
  updateMin(min4) {
    const { input, _picker } = this;
    if (input) {
      if (min4 == null) {
        input.removeAttribute("min");
      } else {
        input.min = min4;
      }
    }
    if (_picker) {
      _picker.minDate = min4;
    }
    this.syncInvalid();
  }
  changeMax(value2) {
    return this.transformDateValue(value2, false);
  }
  updateMax(max3) {
    const { input, _picker } = this;
    if (input) {
      if (max3 == null) {
        input.removeAttribute("max");
      } else {
        input.max = max3;
      }
    }
    if (_picker) {
      _picker.maxDate = max3;
    }
    this.syncInvalid();
  }
  get weekStartDay() {
    return typeof this._weekStartDay === "number" ? this._weekStartDay : DateHelper.weekStartDay;
  }
  updateWeekStartDay(weekStartDay) {
    if (this._picker) {
      this._picker.weekStartDay = weekStartDay;
    }
  }
  changeValue(value2, oldValue) {
    const me = this, newValue = me.transformDateValue(value2), invalidDateStr = me.L("L{invalidDate}")(DateHelper.format(TODAY, me.format));
    if (value2 && !newValue) {
      if (!me.inputting || me.validateOnInput) {
        me.setError(invalidDateStr);
      }
      return;
    }
    me.clearError(invalidDateStr);
    if (me.hasChanged(oldValue, newValue)) {
      return super.changeValue(newValue, oldValue);
    }
    if (!me.inputting) {
      me.syncInputFieldValue();
    }
  }
  updateValue(value2, oldValue) {
    const picker = this._picker;
    if (picker) {
      picker.value = value2;
      if (!this.inputting) {
        picker.activeDate = value2;
      }
    }
    super.updateValue(value2, oldValue);
  }
  changeStep(value2, was) {
    const type = typeof value2;
    if (!value2) {
      return null;
    }
    if (type === "number") {
      value2 = {
        magnitude: Math.abs(value2),
        unit: was ? was.unit : "day"
      };
    } else if (type === "string") {
      value2 = DateHelper.parseDuration(value2);
    }
    if (value2 && value2.unit && value2.magnitude) {
      if (value2.magnitude < 0) {
        value2 = {
          magnitude: -value2.magnitude,
          // Math.abs
          unit: value2.unit
        };
      }
      return value2;
    }
  }
  updateStep(value2) {
    this.element.classList[value2 ? "remove" : "add"]("b-no-steppers");
    this.syncInvalid();
  }
  hasChanged(oldValue, newValue) {
    if ((oldValue == null ? void 0 : oldValue.getTime) && (newValue == null ? void 0 : newValue.getTime) && (!this.keepTime || this.isTimeValue(this.keepTime))) {
      return !DateHelper.isEqual(DateHelper.clearTime(oldValue), DateHelper.clearTime(newValue));
    }
    return super.hasChanged(oldValue && oldValue.getTime(), newValue && newValue.getTime());
  }
  get inputValue() {
    const date2 = this.value;
    return date2 ? DateHelper.format(date2, this.format) : "";
  }
  updateFormat() {
    if (!this.isConfiguring) {
      this.syncInputFieldValue(true);
    }
  }
  //endregion
  //region Localization
  updateLocalization() {
    super.updateLocalization();
    this.syncInputFieldValue(true);
  }
  //endregion
  //region Other
  internalOnKeyEvent(event) {
    var _a5;
    super.internalOnKeyEvent(event);
    if (event.type === "keydown") {
      const me = this;
      let step;
      switch (event.virtualKey) {
        case "Enter":
          me.isValid && ((_a5 = me.picker) == null ? void 0 : _a5.hide());
          break;
        case "ArrowLeft":
          if (me.caretAtStart || me.selectedAll) {
            step = -1;
          }
          break;
        case "ArrowRight":
          if (me.caretAtEnd || me.selectedAll) {
            step = 1;
          }
          break;
        case "NumpadAdd":
          step = 1;
          break;
        case "NumpadSubtract":
          step = -1;
          break;
      }
      if (step && me.step) {
        me.stepDate(step);
        me.selectAll();
        event.preventDefault();
      }
    }
  }
  //endregion
};
//region Config
__publicField(DateField, "$name", "DateField");
__publicField(DateField, "type", "datefield");
__publicField(DateField, "alias", "date");
__publicField(DateField, "configurable", {
  /**
   * Get / set format for date displayed in field (see {@link Core.helper.DateHelper#function-format-static}
   * for formatting options).
   * @member {String} format
   */
  /**
   * Format for date displayed in field. Defaults to using long date format, as defined by current locale (`L`)
   * @config {String}
   * @default
   */
  format: "L",
  /**
   * A flag which indicates whether the date parsing should be strict - meaning if the date
   * is missing a year/month/day part - parsing fails.
   *
   * Turned off by default, meaning default values are substituted for missing parts.
   *
   * @config {Boolean}
   * @default
   */
  strictParsing: false,
  // same for all languages
  fallbackFormat: "YYYY-MM-DD",
  timeFormat: "HH:mm:ss:SSS",
  /**
   * A flag which indicates what time should be used for selected date.
   * `false` by default which means time is reset to midnight.
   *
   * Possible options are:
   * - `false` to reset time to midnight
   * - `true` to keep original time value
   * - `'17:00'` a string which is parsed automatically
   * - `new Date(2020, 0, 1, 17)` a date object to copy time from
   * - `'entered'` to keep time value entered by user (in case {@link #config-format} includes time info)
   *
   * @config {Boolean|Date|String}
   * @default
   */
  keepTime: false,
  /**
   * Format for date in the {@link #config-picker}. Uses localized format per default
   * @config {String}
   */
  pickerFormat: null,
  /**
   * Set to `true` to first clear time of the field's value before comparing it to the max value
   * @internal
   * @config {Boolean}
   */
  validateDateOnly: null,
  /**
   * Widgets that trigger functionality upon click. Each trigger icon is a {@link Core.widget.Widget} instance
   * which may be hidden, shown and observed and styled just like any other widget.
   * @prp {Object<String,Core.widget.Widget>} triggers
   * @property {FieldTriggerConfig} triggers.expand Expands the date picker to select a date
   * @property {FieldTriggerConfig} triggers.back Subtracts the {@link #config-step} from the current date
   * @property {FieldTriggerConfig} triggers.forward Adds the {@link #config-step} to the current date
   * @property {FieldTriggerConfig} triggers.clear Clears the field value, only available if this field is
   * {@link #config-clearable}
   * @accepts {Object<String,FieldTriggerConfig>}
   */
  triggers: {
    expand: {
      cls: "b-icon-calendar",
      handler: "onTriggerClick",
      weight: 200
    },
    back: {
      cls: "b-icon b-icon-angle-left b-step-trigger b-step-trigger-back",
      key: "Shift+ArrowDown",
      handler: "onBackClick",
      align: "start",
      weight: 100
    },
    forward: {
      cls: "b-icon b-icon-angle-right b-step-trigger b-step-trigger-forward",
      key: "Shift+ArrowUp",
      handler: "onForwardClick",
      align: "end",
      weight: 100
    }
  },
  // An optional extra CSS class to add to the picker container element
  calendarContainerCls: "",
  /**
   * Get/set min value, which can be a Date or a string. If a string is specified, it will be converted using
   * the specified {@link #config-format}.
   * @member {Date} min
   * @accepts {String|Date}
   */
  /**
   * Min value
   * @config {String|Date}
   */
  min: null,
  /**
   * Get/set max value, which can be a Date or a string. If a string is specified, it will be converted using
   * the specified {@link #config-format}.
   * @member {Date} max
   * @accepts {String|Date}
   */
  /**
   * Max value
   * @config {String|Date}
   */
  max: null,
  /**
   * The `step` property may be set in object form specifying two properties, `magnitude`, a Number, and
   * `unit`, a String.
   *
   * If a Number is passed, the step's current unit is used (or `day` if no current step set) and just the
   * magnitude is changed.
   *
   * If a String is passed, it is parsed by {@link Core.helper.DateHelper#function-parseDuration-static}, for
   * example `'1d'`, `'1 d'`, `'1 day'`, or `'1 day'`.
   *
   * Upon read, the value is always returned in object form containing `magnitude` and `unit`.
   * @member {DurationConfig} step
   * @accepts {String|Number|DurationConfig}
   */
  /**
   * Time increment duration value. If specified, `forward` and `back` triggers are displayed.
   * The value is taken to be a string consisting of the numeric magnitude and the units.
   * The units may be a recognised unit abbreviation of this locale or the full local unit name.
   * For example `'1d'` or `'1w'` or `'1 week'`. This may be specified as an object containing
   * two properties: `magnitude`, a Number, and `unit`, a String
   * @config {String|Number|DurationConfig}
   */
  step: false,
  /**
   * Set to `false` to hide the forward and backward date step triggers.
   * @config {Boolean}
   * @default true
   */
  stepTriggers: {
    $config: null,
    default: true
  },
  /**
   * The week start day in the {@link #config-picker}, 0 meaning Sunday, 6 meaning Saturday.
   * Uses localized value per default.
   * @config {Number}
   */
  weekStartDay: null,
  /**
   * A config object used to configure the {@link Core.widget.DatePicker datePicker}.
   * ```javascript
   * dateField = new DateField({
   *     picker : {
   *         tbar : {
   *             items : {
   *                 // Remove the prev year and next year buttons by setting them to null.
   *                 // Move the prev month button to just before the next month button by increasing its weight
   *                 prevYear  : null,
   *                 nextYear  : null,
   *                 prevMonth : {
   *                     weight : 390
   *                 }
   *             }
   *         }
   *     }
   * ```
   * @config {DatePickerConfig}
   */
  picker: {
    type: "datepicker",
    floating: true,
    scrollAction: "realign",
    align: {
      align: "t0-b0",
      axisLock: true
    }
  },
  /**
   * Get/set value, which can be set as a Date or a string but always returns a Date. If a string is
   * specified, it will be converted using the specified {@link #config-format}
   * @member {Date} value
   * @accepts {String|Date}
   */
  /**
   * Value, which can be a Date or a string. If a string is specified, it will be converted using the
   * specified {@link #config-format}
   * @config {String|Date}
   */
  value: null,
  role: "combobox"
});
DateField.initClass();
DateField._$name = "DateField";

// ../Core/lib/Core/widget/TimePicker.js
var hours = Array.from(Array(24)).map((_, i) => {
  return { id: i, text: String(i + 100).slice(1) };
});
var seconds = Array.from(Array(60)).map((_, i) => {
  return { id: i, text: String(i + 100).slice(1) };
});
var TimePicker = class extends Panel {
  //endregion
  alignTo() {
    const { widgetMap } = this, { minute, second } = this.owner.input;
    this.getConfig("is24Hour");
    if (this.minimizeHeight) {
      widgetMap.hour.maxHeight = widgetMap.second.maxHeight = widgetMap.minute.height;
    }
    widgetMap.minute[minute ? "show" : "hide"]();
    widgetMap.second[second ? "show" : "hide"]();
    return super.alignTo(...arguments);
  }
  updateIs24Hour(is24Hour) {
    this.widgetMap.hour.store.filter((t) => is24Hour ? true : t.id < 12);
  }
  updateStep(step) {
    step = DateHelper.as("m", step.magnitude, step.unit);
    const minuteValues = [];
    for (let minutes = 0; minutes < 60; minutes += step) {
      minuteValues.push({ id: minutes, text: String(100 + minutes).slice(1) });
    }
    this.widgetMap.minute.store.data = minuteValues;
  }
  //region Event listeners
  onSelectionChange({ source, selected: [selectedItem] }) {
    const me = this;
    me._isUserAction = source._isUserAction;
    source.navigator.activeItem = selectedItem;
    if (!me.updatingValue) {
      me.owner.input.setSubFieldValue(source.ref, selectedItem.id);
    }
    me._isUserAction = false;
  }
  //endregion
  //region Internal functions
  triggerTimeChange(time) {
    this.trigger("timeChange", { time });
  }
  //endregion
  //region Getters / Setters
  updateInitialValue(initialValue) {
    this.value = initialValue;
  }
  changeValue(value2) {
    if (value2) {
      value2 = typeof value2 === "string" ? DateHelper.parse(value2, this.format) : value2;
    }
    return value2 != null ? value2 : DateHelper.getTime(0);
  }
  updateValue(value2) {
    const me = this, {
      hour: hour2,
      minute,
      second
    } = me.widgetMap, h = value2.getHours(), hours2 = me.is24Hour ? h : h % 12, minutes = value2.getMinutes(), seconds2 = value2.getSeconds();
    me.updatingValue = true;
    hour2.navigator.activeItem = new Number(hours2);
    hour2.select(hours2, true);
    minute.navigator.activeItem = new Number(minutes);
    minute.select(minutes, true);
    second.navigator.activeItem = new Number(seconds2);
    second.select(seconds2, true);
    me.updatingValue = false;
    minute.store.filter();
    second.store.filter();
    if (me.isVisible) {
      me.triggerTimeChange(value2);
    }
  }
  changeMin(min4) {
    return typeof min4 === "string" ? DateHelper.parse(min4, this.format) : min4;
  }
  updateMin(min4) {
    const {
      hour: hour2,
      minute,
      second
    } = this.widgetMap, { owner, is24Hour } = this;
    if (min4) {
      const minHours = min4.getHours(), minMinutes = min4.getMinutes(), minSeconds = min4.getSeconds();
      hour2.store.filter({
        id: "min-filter",
        filterBy: ({ id: id2 }) => {
          if (!is24Hour && owner.value.getHours() >= 12) {
            id2 += 12;
          }
          return id2 >= minHours;
        }
      });
      minute.store.filter({
        id: "min-filter",
        // Only filter minutes if the selected hour is the minimum hour
        filterBy: ({ id: id2 }) => owner.value.getHours() !== minHours || id2 >= minMinutes
      });
      second.store.filter({
        id: "min-filter",
        filterBy: ({ id: id2 }) => {
          const { value: value2 } = owner;
          return value2.getHours() * 60 + value2.getMinutes() > minHours * 60 + minMinutes || id2 >= minSeconds;
        }
      });
    } else {
      hour2.store.removeFilter("min-filter");
      minute.store.removeFilter("min-filter");
      second.store.removeFilter("min-filter");
    }
  }
  changeMax(max3) {
    return typeof max3 === "string" ? DateHelper.parse(max3, this.format) : max3;
  }
  updateMax(max3) {
    const {
      hour: hour2,
      minute,
      second
    } = this.widgetMap;
    if (max3) {
      const minHours = max3.getHours(), minMinutes = max3.getMinutes(), minSeconds = max3.getSeconds();
      hour2.store.filter({
        id: "max-filter",
        filterBy: ({ id: id2 }) => id2 <= minHours
      });
      minute.store.filter({
        id: "max-filter",
        filterBy: ({ id: id2 }) => id2 <= minMinutes
      });
      second.store.filter({
        id: "max-filter",
        filterBy: ({ id: id2 }) => id2 <= minSeconds
      });
    } else {
      hour2.store.removeFilter("max-filter");
      minute.store.removeFilter("max-filter");
      second.store.removeFilter("max-filter");
    }
  }
  //endregion
};
//region Config
__publicField(TimePicker, "$name", "TimePicker");
__publicField(TimePicker, "type", "timepicker");
__publicField(TimePicker, "configurable", {
  floating: true,
  layout: "hbox",
  defaults: {
    type: "list",
    displayField: "id",
    itemTpl(r) {
      const value2 = r.id, displayVal = this.ref !== "hour" || this.owner.is24Hour || value2 ? value2 : 12;
      return String(displayVal + 100).slice(1);
    },
    itemCls: "b-timepicker-item",
    itemsFocusable: false,
    scrollable: {
      overflowY: "hidden-scroll"
    },
    onSelectionChange: "up.onSelectionChange",
    navigator: {
      activateOnMouseover: true
    }
  },
  items: {
    hour: {
      items: hours
    },
    minute: {
      items: []
    },
    second: {
      hidden: true,
      items: seconds
    }
  },
  step: {
    magnitude: 5,
    unit: "m"
  },
  autoShow: false,
  trapFocus: true,
  /**
   * Time value, which can be a Date or a string. If a string is specified, it will be converted using the
   * specified {@link #config-format}
   * @prp {Date}
   * @accepts {Date|String}
   */
  value: {
    $config: {
      equal: "date"
    },
    value: null
  },
  /**
   * Time format. Used to set appropriate 12/24 hour format to display.
   * See {@link Core.helper.DateHelper#function-format-static DateHelper} for formatting options.
   * @prp {String}
   */
  format: null,
  /**
   * Max value, which can be a Date or a string. If a string is specified, it will be converted using the
   * specified {@link #config-format}
   * @prp {Date}
   * @accepts {Date|String}
   */
  max: null,
  /**
   * Min value, which can be a Date or a string. If a string is specified, it will be converted using the
   * specified {@link #config-format}
   * @prp {Date}
   * @accepts {Date|String}
   */
  min: null,
  is24Hour: {
    $config: "lazy",
    value: null
  },
  /**
   * Initial value, which can be a Date or a string. If a string is specified, it will be converted using the
   * specified {@link #config-format}. Initial value is restored on Escape click
   * @member {Date} initialValue
   * @accepts {Date|String}
   */
  initialValue: null
  // Not documented as config on purpose, API was that way
});
TimePicker.initClass();
TimePicker._$name = "TimePicker";

// ../Core/lib/Core/widget/TimeField.js
var numbers = /\d+/;
var delimiterRe = /\d+(\D)\d+/;
var names = ["hour", "minute", "second"];
var magnitudes = [3600, 60, 1];
var getMethod = [
  "getHours",
  "getMinutes",
  "getSeconds"
];
var styles = `
        :host {
            display:flex
        }
        * {
            font-size:inherit;
            font-family:inherit;
            color:inherit;
            background-color:inherit
        }
        input,button {
            padding:0;
            margin:0;
            border:0 none;
            outline:0 none;
            background-color:var(--timefield-input-background-color);
            text-align:center
        }
        input {
            width:2ch;
        }
        #am-pm {
            margin-inline:5px
        }
        #am-pm:focus-visible {
            outline:none;
            background-color:var(--timefield-button-background-color)
        }`;
var TimeInput = class extends HTMLElement {
  constructor() {
    var _a5;
    super();
    const me = this, root = me._root = me.attachShadow({
      mode: "open",
      delegatesFocus: true
    });
    me.internals_ = (_a5 = me.attachInternals) == null ? void 0 : _a5.call(me);
    me.byRef = /* @__PURE__ */ Object.create(null);
    root.addEventListener("keydown", me.onKeyDown.bind(me));
    root.addEventListener("focusin", me.onFocusIn.bind(me));
    root.addEventListener("click", me.onClick.bind(me));
    root.addEventListener("input", me.onInput.bind(me));
    root.addEventListener("focusout", me.onFocusOut.bind(me));
  }
  connectedCallback() {
    var _a5;
    const me = this;
    me.silentChangeValue = true;
    me.owner = (_a5 = me.elementData) == null ? void 0 : _a5.owner;
    me.createStructure(me.owner.format);
    me.updateValue(me._value);
    me.silentChangeValue = false;
  }
  ingestDate(d) {
    let r = new Date(d);
    if (isNaN(r)) {
      r = DateHelper.parse(d, this.owner.format);
    }
    return r;
  }
  get is24Hour() {
    return this.owner.is24Hour;
  }
  attributeChangedCallback(name, oldValue, value2) {
    var _a5;
    const me = this;
    switch (name) {
      case "min":
        me.updateMin(value2);
        break;
      case "max":
        me.updateMax(value2);
        break;
      case "label":
        me._label = value2;
        (_a5 = me.inputs) == null ? void 0 : _a5.forEach((e) => e.setAttribute("aria-label", value2));
        break;
      case "disabled":
        me.disabled = value2;
        break;
      case "tabindex":
        me.tabIndex = Number(value2);
        break;
    }
  }
  updateMin(min4) {
    const d = min4 == null ? null : DateHelper.getTime(this.ingestDate(min4));
    this.minValue = d;
    this.minHour = (d == null ? void 0 : d.getHours()) || 0;
  }
  updateMax(max3) {
    const d = max3 == null ? null : DateHelper.getTime(this.ingestDate(max3));
    this.maxValue = d;
    this.maxHour = (d == null ? void 0 : d.getHours()) || (this.is24Hour ? 23 : 11);
  }
  /**
   * TimeFields always focus the first (Hours) subfield when focused
   */
  onFocusIn(event) {
    var _a5;
    const me = this, { target } = event, ariaLabel = me.getAriaLabel();
    if (target.tagName === "INPUT") {
      target.value = `00${target.value}`.slice(-2);
      (_a5 = target.setSelectionRange) == null ? void 0 : _a5.call(target, 0, 2);
    }
    me.setAttribute("aria-label", ariaLabel);
    me._root.activeElement.setAttribute("aria-label", ariaLabel);
    if (!me._root.contains(event.relatedTarget)) {
      if (!GlobalEvents_default.isMouseDown()) {
        me.focusField(0);
      }
    }
    event.stopImmediatePropagation();
  }
  onClick(event) {
    const { target } = event, { owner } = this;
    if (target === this.ampmButton) {
      if (!owner.readOnly) {
        target.innerText = target.innerText === DateHelper.amIndicator ? DateHelper.pmIndicator : DateHelper.amIndicator;
        this.onValueMutated();
        event.stopPropagation();
      }
    }
  }
  onFocusOut({ target }) {
    this.onValueMutated();
    if (!this.isCleared && target !== this.ampmButton) {
      target.value = `0${parseInt(target.value)}`.slice(-2);
    }
    ;
  }
  onKeyDown(keyEvent) {
    var _a5, _b2, _c2, _d;
    const me = this, {
      subfieldIndex
    } = me, {
      editable,
      readOnly
    } = me.owner, {
      amIndicator: amIndicator2,
      pmIndicator: pmIndicator2
    } = DateHelper, { target, key } = keyEvent;
    if (keyEvent.ctrlKey || keyEvent.cmdKey || keyEvent.metaKey || keyEvent.shiftKey && key !== "Tab" && key.length !== 1) {
      return;
    }
    switch (key) {
      case "Tab":
        me.inputs[keyEvent.shiftKey ? 0 : me.inputs.length - 1].focus();
        break;
      case " ":
        if (target === me.ampmButton || readOnly) {
          keyEvent.stopImmediatePropagation();
        }
        break;
      case "ArrowLeft":
        keyEvent.preventDefault();
        keyEvent.stopPropagation();
        if (subfieldIndex > 0 && !keyEvent.altKe) {
          me.focusField(subfieldIndex - 1);
        }
        break;
      case ":":
        if (target === me.ampmButton) {
          keyEvent.preventDefault();
          break;
        }
      case "ArrowRight":
        keyEvent.preventDefault();
        keyEvent.stopPropagation();
        if (subfieldIndex < me.inputs.length - 1) {
          me.focusField(subfieldIndex + 1);
        }
        break;
      case "ArrowUp":
        keyEvent.preventDefault();
        keyEvent.stopPropagation();
        if (!readOnly) {
          me[me.owner.pickerVisible ? "decrementSubfield" : "incrementSubfield"](target);
        }
        break;
      case "ArrowDown":
        keyEvent.preventDefault();
        keyEvent.stopPropagation();
        if (!readOnly) {
          me[me.owner.pickerVisible ? "incrementSubfield" : "decrementSubfield"](target);
        }
        break;
      case "0":
      case "1":
      case "2":
      case "3":
      case "4":
      case "5":
      case "6":
      case "7":
      case "8":
      case "9":
        if (!editable || readOnly) {
          keyEvent.preventDefault();
        }
        break;
      case "Backspace":
      case "Delete":
        if (!editable || readOnly) {
          keyEvent.preventDefault();
        }
        break;
      case "Escape":
        break;
      case ((_a5 = amIndicator2 == null ? void 0 : amIndicator2[0]) == null ? void 0 : _a5.toLocaleLowerCase()):
      case ((_b2 = amIndicator2 == null ? void 0 : amIndicator2[0]) == null ? void 0 : _b2.toLocaleUpperCase()):
        if (!readOnly && !me.is24Hour) {
          keyEvent.preventDefault();
          me.ampmButton.innerText = amIndicator2;
          me.onValueMutated();
          break;
        }
      case ((_c2 = pmIndicator2 == null ? void 0 : pmIndicator2[0]) == null ? void 0 : _c2.toLocaleLowerCase()):
      case ((_d = pmIndicator2 == null ? void 0 : pmIndicator2[0]) == null ? void 0 : _d.toLocaleUpperCase()):
        keyEvent.preventDefault();
        if (!readOnly && !me.is24Hour) {
          keyEvent.preventDefault();
          me.ampmButton.innerText = pmIndicator2;
          me.onValueMutated();
          break;
        }
      default:
        keyEvent.preventDefault();
    }
  }
  onInput({ target, data }) {
    var _a5;
    const me = this;
    let { value: value2 } = target;
    if ((data == null ? void 0 : data.length) > 1 && !isFinite(value2)) {
      value2 = target.value = ((_a5 = me.value) == null ? void 0 : _a5.getHours()) || "00";
    } else if (value2.length === 1 || value2.length === 2 && target.selectionStart === 1 && target.selectionEnd === 1) {
      target.setSelectionRange(1, 2);
      return;
    } else if (!value2) {
      value2 = target.value = "00";
      target.setSelectionRange(0, 2);
    } else if (target.selectionEnd === 2) {
      const { subfieldIndex } = me;
      if (subfieldIndex < me.inputs.length - 1) {
        me.focusField(subfieldIndex + 1);
      }
    }
    if (target === me.hour && value2 === "00" && !me.is24Hour) {
      me.ampmButton.innerText = DateHelper.amIndicator;
    }
    me.activeElement = target;
    me.onValueMutated();
    me.activeElement = null;
  }
  /**
   * Decrement the focused subfield (Hours, minutes or seconds).
   *
   * If the configured {@link #config-step}'s unit matches the subfield's unit,
   * the step magnitude is used, otherwise -1 is used.
   * @internal
   */
  decrementSubfield(field) {
    var _a5;
    const {
      subfieldIndex,
      step,
      isPM
    } = this;
    if (field === this.ampmButton) {
      field.innerText = isPM ? DateHelper.amIndicator : DateHelper.pmIndicator;
    } else {
      const oldValue = this.getSubFieldValue(subfieldIndex), increment = step < magnitudes[subfieldIndex - 1] && step > magnitudes[subfieldIndex + 1] ? step / magnitudes[subfieldIndex] : 1, newValue = Math.floor(oldValue / increment) * increment - increment;
      if (subfieldIndex === 0 && !this.is24Hour && oldValue === 12) {
        this.ampmButton.innerText = DateHelper.amIndicator;
      }
      field.value = newValue;
    }
    this.onValueMutated();
    (_a5 = field.setSelectionRange) == null ? void 0 : _a5.call(field, 0, 2);
  }
  /**
   * Increment the focused subfield (Hours, minutes or seconds).
   *
   * If the configured {@link #config-step}'s unit matches the subfield's unit,
   * the step magnitude is used, otherwise -1 is used.
   * @internal
   */
  incrementSubfield(field) {
    var _a5;
    const {
      subfieldIndex,
      step,
      is24Hour,
      isPM
    } = this;
    if (field === this.ampmButton) {
      field.innerText = isPM ? DateHelper.amIndicator : DateHelper.pmIndicator;
    } else {
      const field2 = this.inputs[subfieldIndex], oldValue = this.getSubFieldValue(subfieldIndex), increment = step < magnitudes[subfieldIndex - 1] && step > magnitudes[subfieldIndex + 1] ? step / magnitudes[subfieldIndex] : 1, newValue = Math.floor(oldValue / increment) * increment + increment;
      if (subfieldIndex === 0 && !this.is24Hour && oldValue === 11) {
        this.ampmButton.innerText = DateHelper.pmIndicator;
      }
      if (subfieldIndex === 0) {
        const hourOfDay = isPM && !is24Hour ? oldValue + 12 : oldValue;
        if (hourOfDay === (this.maxHour || 23)) {
          return;
        }
      }
      field2.value = newValue;
    }
    this.onValueMutated();
    (_a5 = field.setSelectionRange) == null ? void 0 : _a5.call(field, 0, 2);
  }
  /**
   * Creates the element structure:
   *
   * ```html
   * <span></span><input><span></span><input><span></span><input><span></span>
   * ```
   *
   * Each time token, hours, minutes, seconds has an input field and there may be
   * boilerplate text from the format string at any positoin before, between or after them.
   * @param {String} format The {@link Core.helper.DateHelper} format string for the time field.
   * @returns {String} the shadow root's HTML sting.
   */
  createStructure(format2 = this.owner.format) {
    var _a5;
    const me = this, {
      _root,
      tabIndex,
      is24Hour,
      silentChangeValue
    } = me, {
      amIndicator: amIndicator2
    } = DateHelper, testDate = new Date(1970, 0, 1, 1, 2, 3), timeString = DateHelper.format(testDate, format2).trim(), delimiter = me.delimiter = (_a5 = timeString.match(delimiterRe)) == null ? void 0 : _a5[1], parts = [...timeString.matchAll(new RegExp(`\\d+${delimiter ? `|\\${delimiter}` : ""}|${amIndicator2}|[^\\${delimiter}\\d]+`, "g"))].map((m) => m[0]), children = [];
    me.names = [];
    me.labelElId = `${me.owner.id}-label-element`;
    me.silentChangeValue = true;
    if (tabIndex != null) {
      me.setAttribute("tabIndex", tabIndex);
    }
    let subfieldIndex = 0, hasAmPmIndicator;
    for (let i = 0, inputCount = 0, { length } = parts; i < length; i++) {
      const part = parts[i], max3 = i ? 59 : is24Hour ? 23 : 11;
      if (part.trim() === amIndicator2) {
        hasAmPmIndicator = true;
        me.names.push(`${DateHelper.amIndicator}/${DateHelper.pmIndicator}`);
        children.push({
          tag: "button",
          "aria-label": me.getAriaLabel(subfieldIndex),
          reference: "ampmButton",
          id: "am-pm",
          type: "button",
          text: amIndicator2,
          dataset: {
            subfieldIndex: subfieldIndex++
          }
        });
      } else if (part.match(numbers)) {
        me.names.push(DateHelper.getLocalizedNameOfUnit(names[inputCount]));
        children.push({
          tag: "input",
          "aria-label": me.getAriaLabel(subfieldIndex),
          id: names[inputCount],
          reference: names[inputCount++],
          size: 2,
          maxlength: 2,
          minlength: 2,
          dataset: {
            subfieldIndex: subfieldIndex++,
            max: max3
          }
        });
      } else if (part === delimiter) {
        children.push({
          tag: "span",
          text: delimiter,
          reference: `delimiter`
        });
      } else {
        children.push({
          reference: "junk",
          tag: "span",
          text: part.replaceAll(" ", "\xA0")
        });
      }
    }
    if (children[children.length - 1].reference === "delimiter") {
      children.length--;
    }
    if (!is24Hour && !hasAmPmIndicator) {
      me.names.push(`${DateHelper.amIndicator}/${DateHelper.pmIndicator}`);
      children.push({
        tag: "button",
        reference: "ampmButton",
        id: "am-pm",
        type: "button",
        text: amIndicator2,
        dataset: {
          subfieldIndex: subfieldIndex++
        }
      });
    }
    me.silentChangeValue = silentChangeValue;
    children.push({
      tag: "style",
      text: styles
    });
    DomSync.sync({
      targetElement: _root,
      refOwner: me,
      domConfig: {
        onlyChildren: true,
        children
      }
    });
    me.inputs = [..._root.querySelectorAll("input")];
    if (!me.minute) {
      me.owner.step = "1 h";
    }
    if (!is24Hour) {
      me.inputs.push(me.ampmButton);
    } else {
      me.ampmButton = null;
    }
  }
  attachRef(name, el) {
    this.byRef[name] = this[name] = el;
  }
  detachRef(name) {
    this[name] = null;
    delete this.byRef[name];
  }
  getAriaLabel(subfieldIndex = this.subfieldIndex) {
    const { owner } = this;
    return `${owner.ariaLabel || owner.label || ""} ${this.innerText}${subfieldIndex != null ? ` - ${this.names[subfieldIndex]}` : ""}.`;
  }
  getSubFieldValue(index) {
    const { date: date2 } = this;
    if (date2) {
      return date2[getMethod[index]]();
    } else {
      return 0;
    }
  }
  setSubFieldValue(field, value2, silent) {
    field = typeof field === "number" ? this.inputs[field] : typeof field === "string" ? this[field] : field;
    if (field) {
      if (value2 < 10 && (field !== this.activeElement || field.value.length === 2)) {
        value2 = `0${value2}`;
      }
      if (field.value != value2) {
        field.value = value2;
        if (!silent) {
          this.onValueMutated();
        }
      }
    }
  }
  set value(value2) {
    const { _value: oldValue } = this;
    value2 = this._value = value2 ? this.ingestDate(value2) : null;
    if (this.isConnected && value2 - oldValue) {
      this.updateValue(value2, oldValue);
    }
  }
  updateValue(value2, oldValue = null) {
    var _a5;
    if (Number(value2) != Number(oldValue)) {
      const me = this, { ampmButton } = me, ariaLabel = me.getAriaLabel();
      if (value2) {
        const { hour: hour2, minute, second } = me, h = value2.getHours();
        if (me.is24Hour) {
          me.setSubFieldValue(hour2, h, true);
        } else {
          ampmButton.innerText = h > 11 ? DateHelper.pmIndicator : DateHelper.amIndicator;
          me.setSubFieldValue(hour2, h > 12 ? h % 12 : h, true);
        }
        minute && me.setSubFieldValue(minute, value2.getMinutes(), true);
        second && me.setSubFieldValue(second, value2.getSeconds(), true);
      } else {
        me._value = DateHelper.clearTime(oldValue);
        me.inputs.forEach((i) => i.value = "");
        ampmButton && (ampmButton.innerText = DateHelper.amIndicator);
      }
      me.setAttribute("value", DateHelper.format(value2, me.owner.format));
      (_a5 = me._root.activeElement) == null ? void 0 : _a5.setAttribute("aria-label", ariaLabel);
      me.setAttribute("aria-label", ariaLabel);
      if (!me.silentChangeValue && !me.owner.sycingInputFieldValue) {
        me.dispatchEvent(new InputEvent("change"));
      }
    }
  }
  onValueMutated() {
    const me = this, {
      date: date2,
      minValue,
      maxValue
    } = me, newValue = date2 ? new Date(date2) : null;
    if (date2) {
      if (minValue && DateHelper.getTime(newValue) < DateHelper.getTime(minValue)) {
        newValue.setHours(minValue.getHours(), minValue.getMinutes(), minValue.getSeconds());
        me._value = null;
      } else if (maxValue && DateHelper.getTime(newValue) > DateHelper.getTime(maxValue)) {
        newValue.setHours(maxValue.getHours(), maxValue.getMinutes(), maxValue.getSeconds());
        me._value = null;
      }
    }
    me.value = newValue;
  }
  set disabled(disabled) {
    disabled = Boolean(disabled);
    if (this._disabled !== disabled) {
      this._disabled = disabled;
      if (disabled) {
        this.setAttribute("disabled", true);
      } else {
        this.removeAttribute("disabled");
      }
    }
  }
  set tabIndex(tabIndex) {
    this._tabIndex = tabIndex;
  }
  get tabIndex() {
    return this._tabIndex;
  }
  get subfieldIndex() {
    const { activeElement } = this.shadowRoot;
    return activeElement && parseInt(activeElement.dataset.subfieldIndex);
  }
  get isPM() {
    var _a5;
    return this.is24Hour ? parseInt(this.hour.value || 0) > 11 : ((_a5 = this.ampmButton.firstChild) == null ? void 0 : _a5.data) === DateHelper.pmIndicator;
  }
  get value() {
    return this.date;
  }
  get innerText() {
    return DateHelper.format(this.silentChangeValue ? this._value : this.date, this.owner.format) || "";
  }
  get date() {
    var _a5, _b2, _c2, _d;
    const me = this, { _value } = me;
    if (!me.isConnected) {
      return _value || null;
    }
    if (me.isCleared) {
      return null;
    }
    const args = me._value == null ? [] : [me._value], result = new Date(...args), h = parseInt(((_a5 = me.hour) == null ? void 0 : _a5.value) || 0), minutes = parseInt(((_b2 = me.minute) == null ? void 0 : _b2.value) || 0), seconds2 = parseInt(((_c2 = me.second) == null ? void 0 : _c2.value) || 0);
    let hours2 = h;
    if (!me.is24Hour) {
      const isPM = ((_d = me.ampmButton.firstChild) == null ? void 0 : _d.data) === DateHelper.pmIndicator;
      if (h === 12 && !me.isPM) {
        hours2 = 0;
      } else if (h < 12 && isPM) {
        hours2 += 12;
      }
    }
    result.setHours(hours2, minutes, seconds2, 0);
    return result;
  }
  get rawDate() {
    var _a5, _b2, _c2;
    const result = new Date(this._value);
    result.setHours(parseInt(((_a5 = this.hour) == null ? void 0 : _a5.value) || 0), parseInt(((_b2 = this.minute) == null ? void 0 : _b2.value) || 0), parseInt(((_c2 = this.second) == null ? void 0 : _c2.value) || 0));
    return result;
  }
  get isCleared() {
    var _a5;
    return Boolean(!((_a5 = this.inputs) == null ? void 0 : _a5.map((i) => i.value.trim()).join("")));
  }
  focusField(index) {
    var _a5;
    const input = this.inputs[index];
    if (input) {
      input.focus();
      (_a5 = input.setSelectionRange) == null ? void 0 : _a5.call(input, 0, 2);
    }
  }
};
__publicField(TimeInput, "formAssociated", true);
__publicField(TimeInput, "observedAttributes", [
  "min",
  "max",
  "disabled",
  "tabindex"
]);
if (typeof customElements !== "undefined" && !customElements.get("bry-time")) {
  customElements.define("bry-time", TimeInput);
}
var TimeField = class extends PickerField {
  //endregion
  get inputElement() {
    const domConfig = super.inputElement;
    domConfig.tag = "bry-time";
    domConfig.label = this.label;
    domConfig.elementData = { owner: this };
    return domConfig;
  }
  //region Init & destroy
  changePicker(picker, oldPicker) {
    const me = this, pickerWidth = me.pickerWidth || (picker == null ? void 0 : picker.width);
    return TimePicker.reconfigure(oldPicker, picker, {
      owner: me,
      defaults: {
        step: me.step,
        value: me.value,
        forElement: me.pickerAlignElement,
        owner: me,
        is24Hour: me.is24Hour,
        align: {
          matchSize: pickerWidth == null,
          anchor: me.overlayAnchor,
          target: me[me.pickerAlignElement]
        },
        width: pickerWidth,
        onTimeChange({ source, time }) {
          me._isUserAction = true;
          source._isUserAction && source.hide();
          me.value = time;
          if (me.min && !me.is24Hour) {
            me._picker.widgetMap.hour.store.filter();
          }
          me._isUserAction = false;
        }
      }
    });
  }
  //endregion
  selectAll() {
  }
  syncInputFieldValue() {
    const me = this;
    me.sycingInputFieldValue = true;
    super.syncInputFieldValue(...arguments);
    me.sycingInputFieldValue = false;
    me.syncInvalid();
    if (me.pickerVisible) {
      me.picker.value = me.value;
    }
  }
  get inputValue() {
    return this._value;
  }
  get needsInputSync() {
    var _a5;
    return String(this.input[this.inputValueAttr]) !== String((_a5 = this.inputValue) != null ? _a5 : "");
  }
  // Override at this level. We set the attribute.
  // But the custom element itself decides on its editability by interrogating its owner.
  syncInputReadOnly() {
    this.getConfig("readOnly");
    this.inputReadOnly = this._readOnly;
  }
  onBackClick() {
    const me = this, { min: min4 } = me;
    if (!me.readOnly && me.value) {
      const newValue = DateHelper.add(me.value, -1 * me.step.magnitude, me.step.unit);
      if (!min4 || min4.getTime() <= newValue) {
        me.value = newValue;
      }
    }
  }
  onForwardClick() {
    const me = this, { max: max3 } = me;
    if (!me.readOnly && me.value) {
      const newValue = DateHelper.add(me.value, me.step.magnitude, me.step.unit);
      if (!max3 || max3.getTime() >= newValue) {
        me.value = newValue;
      }
    }
  }
  //endregion
  // region Validation
  get isValid() {
    const me = this;
    me.clearError("L{minimumValueViolation}", true);
    me.clearError("L{maximumValueViolation}", true);
    let value2 = me.input.date;
    if (value2) {
      value2 = DateHelper.getTime(value2);
      if (me._min && DateHelper.getTime(me._min) > value2) {
        me.setError("L{minimumValueViolation}", true);
        return false;
      }
      if (me._max && DateHelper.getTime(me._max) < value2) {
        me.setError("L{maximumValueViolation}", true);
        return false;
      }
    }
    return super.isValid;
  }
  internalOnChange(event) {
    const me = this, value2 = me.input.date;
    if (!value2 || !isNaN(value2)) {
      me.clearError("L{invalidTime}");
      me.inputting = true;
      me._value = value2;
      if (me.pickerVisible) {
        me.picker.value = value2;
      }
      super.internalOnChange(...arguments);
      me.inputting = false;
    }
  }
  internalOnKeyEvent(event) {
    if (this.pickerVisible) {
      if (event.key === "Escape") {
        this.value = this.pickerValueOnShow;
      }
      if (event.key === "Enter") {
        this.hidePicker();
        event.preventDefault();
        return;
      }
    }
    super.internalOnKeyEvent(event);
  }
  hasChanged(oldValue, newValue) {
    if ((oldValue == null ? void 0 : oldValue.getTime) && (newValue == null ? void 0 : newValue.getTime)) {
      return this.keepDate ? oldValue - newValue !== 0 : !DateHelper.isSameTime(oldValue, newValue);
    }
    return super.hasChanged(oldValue, newValue);
  }
  //endregion
  //region Toggle picker
  /**
   * Show picker
   */
  showPicker() {
    const me = this, {
      picker,
      value: value2
    } = me;
    if (me.readOnly) {
      return;
    }
    picker.value = null;
    picker.format = me.format;
    super.showPicker();
    picker.value = me.pickerValueOnShow = value2;
    picker.max = me.max;
    picker.min = me.min;
    if (!value2) {
      me.value = picker.value;
    }
  }
  //endregion
  //region Getters/setters
  transformTimeValue(value2) {
    if (value2 != null) {
      if (typeof value2 === "string") {
        value2 = DateHelper.parse(value2, this.format);
        if (this.keepDate && value2 && this.value) {
          value2 = DateHelper.copyTimeValues(new Date(this.value), value2);
        }
      } else {
        value2 = new Date(value2);
      }
      if (DateHelper.isValidDate(value2)) {
        if (!this.keepDate) {
          value2 = DateHelper.getTime(value2);
        }
      } else {
        value2 = null;
      }
    }
    const eventObject = { value: value2 };
    this.trigger("transformTimeValue", eventObject);
    value2 = eventObject.value;
    return value2;
  }
  changeMin(value2) {
    return this.transformTimeValue(value2);
  }
  updateMin(value2) {
    const { input } = this;
    if (input) {
      if (value2 == null) {
        input.removeAttribute("min");
      } else {
        input.setAttribute("min", value2);
      }
    }
    this.syncInvalid();
  }
  changeMax(value2) {
    return this.transformTimeValue(value2);
  }
  updateMax(value2) {
    const { input } = this;
    if (input) {
      if (value2 == null) {
        input.removeAttribute("max");
      } else {
        input.setAttribute("max", value2);
      }
    }
    this.syncInvalid();
  }
  changeValue(value2, was) {
    const me = this, newValue = me.transformTimeValue(value2);
    if (value2 && !newValue || me.isRequired && value2 === "") {
      me.setError("L{invalidTime}");
      return;
    }
    me.clearError("L{invalidTime}");
    if (me.hasChanged(was, newValue)) {
      return super.changeValue(newValue, was);
    }
    if (!me.inputting) {
      me.syncInputFieldValue(true);
    }
  }
  updateValue(value2, was) {
    const { expand } = this.triggers;
    this.syncInputFieldValue(true);
    if (expand && value2) {
      expand.element.firstElementChild.style.animationDelay = -((value2.getHours() * 60 + value2.getMinutes()) / 10) + "s";
    }
    super.updateValue(value2, was);
  }
  changeStep(value2, was) {
    const type = typeof value2;
    if (!value2) {
      return { magnitude: 5, unit: "minute" };
    }
    if (type === "number") {
      value2 = {
        magnitude: Math.abs(value2),
        unit: was ? was.unit : "hour"
      };
    } else if (type === "string") {
      value2 = DateHelper.parseDuration(value2);
    }
    if ((value2 == null ? void 0 : value2.unit) && (value2 == null ? void 0 : value2.magnitude)) {
      if (value2.magnitude < 0) {
        value2 = {
          magnitude: -value2.magnitude,
          // Math.abs
          unit: value2.unit
        };
      }
      return value2;
    }
  }
  updateStep(value2) {
    const { _picker } = this;
    this.input.step = value2 && DateHelper.as("s", value2.magnitude, value2.unit);
    _picker && DateHelper.as("m", value2) < 30 && (_picker.step = value2);
    this.syncInvalid();
  }
  get is24Hour() {
    return DateHelper.is24HourFormat(this.format);
  }
  updateFormat(format2, oldFormat) {
    const me = this;
    if (!me.isConfiguring) {
      me._format = oldFormat;
      const { input } = me, { value: value2 } = input;
      me._format = format2;
      me.sycingInputFieldValue = true;
      input.createStructure(format2);
      input.updateValue(value2, NaN);
      me.syncInputFieldValue(true);
      me.sycingInputFieldValue = false;
    }
  }
  //endregion
  //region Localization
  updateLocalization() {
    super.updateLocalization();
    if (this.input.isConnected) {
      this.input.createStructure();
    }
    this.syncInputFieldValue(true);
  }
  //endregion
};
//region Config
__publicField(TimeField, "$name", "TimeField");
__publicField(TimeField, "type", "timefield");
__publicField(TimeField, "alias", "time");
__publicField(TimeField, "configurable", {
  inputElementTag: "bry-time",
  picker: {
    type: "timepicker",
    scrollAction: "realign",
    align: {
      align: "t0-b0",
      minHeight: 200,
      axisLock: true,
      matchSize: "min"
    }
  },
  role: "textbox",
  /**
   * Get/Set format for time displayed in field (see {@link Core.helper.DateHelper#function-format-static}
   * for formatting options).
   * @member {String} format
   */
  /**
   * Format for date displayed in field (see Core.helper.DateHelper#function-format-static for formatting
   * options).
   * @config {String}
   * @default
   */
  format: "LT",
  /**
   * Widgets that trigger functionality upon click. Each trigger icon is a {@link Core.widget.Widget} instance
   * which may be hidden, shown and observed and styled just like any other widget.
   * @prp {Object<String,Core.widget.Widget>} triggers
   * @accepts {Object<String,FieldTriggerConfig>}
   * @property {FieldTriggerConfig} triggers.expand Expands the picker to select a time
   * @property {FieldTriggerConfig} triggers.back Subtracts the {@link #config-step} from the current time
   * @property {FieldTriggerConfig} triggers.forward Adds the {@link #config-step} to the current time
   * @property {FieldTriggerConfig} triggers.clear Clears the field value, only available if this field is
   * {@link #config-clearable}
   */
  triggers: {
    expand: {
      align: "end",
      handler: "onTriggerClick",
      key: " ",
      compose: () => ({
        children: [{
          class: {
            "b-icon-clock-live": 1
          }
        }]
      })
    },
    back: {
      align: "start",
      cls: "b-icon b-icon-angle-left b-step-trigger",
      key: "Shift+ArrowDown",
      handler: "onBackClick"
    },
    forward: {
      align: "end",
      cls: "b-icon b-icon-angle-right b-step-trigger",
      key: "Shift+ArrowUp",
      handler: "onForwardClick"
    }
  },
  /**
   * Get/set min value, which can be a Date or a string. If a string is specified, it will be converted using
   * the specified {@link #config-format}.
   * @member {Date} min
   * @accepts {String|Date}
   */
  /**
   * Min time value
   * @config {String|Date}
   */
  min: null,
  /**
   * Get/set max value, which can be a Date or a string. If a string is specified, it will be converted using
   * the specified {@link #config-format}.
   * @member {Date} max
   * @accepts {String|Date}
   */
  /**
   * Max time value
   * @config {String|Date}
   */
  max: null,
  /**
   * The `step` property may be set in Object form specifying two properties, `magnitude`, a Number, and
   * `unit`, a String.
   *
   * If a Number is passed, the steps's current unit is used and just the magnitude is changed.
   *
   * If a String is passed, it is parsed by {@link Core.helper.DateHelper#function-parseDuration-static}, for
   * example `'5m'`, `'5 m'`, `'5 min'`, `'5 minutes'`.
   *
   * Upon read, the value is always returned in object form containing `magnitude` and `unit`.
   * @member {DurationConfig} step
   * @accepts {String|Number|DurationConfig}
   */
  /**
   * Time increment duration value. Defaults to 5 minutes.
   * The value is taken to be a string consisting of the numeric magnitude and the units.
   * The units may be a recognised unit abbreviation of this locale or the full local unit name.
   * For example `"10m"` or `"5min"` or `"2 hours"`
   * @config {String|Number|DurationConfig}
   */
  step: "5m",
  /**
   * Set to `false` to hide the forward and backward time step triggers.
   * @config {Boolean}
   * @default true
   */
  stepTriggers: {
    $config: null,
    default: true
  },
  /**
   * Get/set value, which can be a Date or a string. If a string is specified, it will be converted using the
   * specified {@link #config-format}.
   * {@note}This value is a {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date Date} object. As such it contains the date part. When set
   * using a string, the date part is set to January 1st of the year 2020.{/@note}
   * @member {Date} value
   * @accepts {String|Date}
   */
  /**
   * Value, which can be a Date or a string. If a string is specified, it will be converted using the
   * specified {@link #config-format}.
   * {@note}When converted to a {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date Date}, the date part is set to January 1st of the year 2020.{/@note}
   * @config {String|Date}
   */
  value: {
    $config: {
      equal: "date"
    },
    value: null
  },
  /**
   * Set to true to not clean up the date part of the passed value. Set to false to reset the date part to
   * January 1st
   * @prp {Boolean}
   * @default
   */
  keepDate: true
});
__publicField(TimeField, "delayable", {
  // Buffer the syncing of validity so that momentary invalid states
  // encountered during typing are suppressed if typing is at reasonable speed.
  syncInvalid: {
    type: "buffer",
    delay: 300
  }
});
TimeField.initClass();
TimeField._$name = "TimeField";

// ../Core/lib/Core/widget/mixin/AbstractDateRange.js
var AbstractDateRange_default = (Target) => {
  var _a5;
  return _a5 = class extends Target.mixin(Fencible_default) {
    // dateFieldDefaults
    get dateFieldDefaults() {
      return ObjectHelper.merge({
        format: this.format
      }, this._dateFieldDefaults);
    }
    // fieldEndDate
    configureDateField(config, existing, which) {
      const me = this, { dateStepTriggers } = me;
      return DateField.reconfigure(existing, config, {
        owner: me,
        defaults: ObjectHelper.assign({
          stepTriggers: dateStepTriggers === true || dateStepTriggers === "both" || dateStepTriggers === which
        }, me.dateFieldDefaults)
      });
    }
    changeFieldEndDate(config, existing) {
      return this.configureDateField(config, existing, "end");
    }
    updateFieldEndDate(item) {
      var _a6;
      item && ((_a6 = this.dateFieldContainer) == null ? void 0 : _a6.add(item));
    }
    // fieldStartDate
    changeFieldStartDate(config, existing) {
      return this.configureDateField(config, existing, "start");
    }
    updateFieldStartDate(item) {
      var _a6;
      item && ((_a6 = this.dateFieldContainer) == null ? void 0 : _a6.add(item));
    }
    // format
    updateFormat(format2) {
      if (!this.isConstructing) {
        this.fieldEndDate.format = format2;
        this.fieldStartDate.format = format2;
      }
    }
    // pickingStartDate
    updatePickingStartDate(pickingStartDate) {
      this.element.classList.toggle("b-picking-start-date", Boolean(pickingStartDate));
    }
    // value
    updateValue(value2, oldValue) {
      var _a6;
      (_a6 = super.updateValue) == null ? void 0 : _a6.call(this, value2, oldValue);
      this.syncValue("value", value2);
    }
    //---------------------------------------------------------------------------------------------------------
    stepDown(event) {
      this.adjustByKey(event, this.stepSmall);
    }
    stepUp(event) {
      this.adjustByKey(event, "-" + this.stepSmall);
    }
    stepDownLarge(event) {
      this.adjustByKey(event, this.stepLarge);
    }
    stepUpLarge(event) {
      this.adjustByKey(event, "-" + this.stepLarge);
    }
    /**
     * Adjusts the date range using the provided keyboard `event` based on the focused date field. If `fieldStartDate`
     * is focused, the start date is adjusted. If `fieldEndDate` is focused, the end date is adjusted. If neither field
     * is focused, the date range is not adjusted.
     * @param {Event} event The keyboard event
     * @param {String} step The amount to step
     * @internal
     */
    adjustByKey(event, step) {
      var _a6, _b2;
      const me = this, { _isUserAction } = me, { target } = event, fields = [me.fieldStartDate, me.fieldEndDate], index = (target === fields[0].input && 1 || target === fields[1].input && 2 || 0) - 1, field = fields[index], value2 = [fields[0].value, fields[1].value], empty2 = value2[0] == null || value2[1] == null;
      value2[index] = DateHelper.add((_a6 = value2[index]) != null ? _a6 : me.defaultDate(index, value2), step);
      value2[1 - index] = (_b2 = value2[1 - index]) != null ? _b2 : value2[index];
      if (value2[1] < value2[0]) {
        value2[1 - index] = value2[index];
      }
      if (me.checkValid(value2)) {
        me._isUserAction = true;
        if (empty2) {
          me.value = value2;
        } else {
          fields[0].value = value2[0];
          fields[1].value = value2[1];
        }
        me._isUserAction = _isUserAction;
      }
      field.selectAll();
      event.preventDefault();
    }
    /**
     * Checks the given `Date` or array of `Date`s to see if any are out of the allow min/max range. Returns 0 if all
     * dates are in range, -1 if a date is below the minimum, or 1 if a date is above the maximum.
     * @param {Date|Date[]} value One or more `Date` values to check.
     * @returns {Number}
     * @internal
     */
    checkMinMax(value2 = this.value) {
      const { maxDate, minDate, validateDateOnly } = this;
      let ret = 0, v, values;
      if (value2 && (minDate || maxDate)) {
        values = Array.isArray(value2) ? value2 : [value2];
        for (v of values) {
          v = validateDateOnly ? DateHelper.clearTime(v) : v;
          ret = minDate && v < minDate ? -1 : maxDate && v > maxDate ? 1 : 0;
          if (ret) {
            break;
          }
        }
      }
      return ret;
    }
    /**
     * Checks the given `Date` or array of `Date`s to see if any are out of the allow min/max range. Returns `true` if
     * all dates are in range, or `false` otherwise.
     * @param {Date|Date[]} value One or more `Date` values to check.
     * @returns {Boolean}
     * @internal
     */
    checkValid(value2 = this.value) {
      return !this.checkMinMax(value2);
    }
    /**
     * Returns a date in the range of `minDate and `maxDate`. This is called when a field's current value is `null`
     * and a default value is needed.
     * @param {Number} index Either 0 for `fieldStartDate` or 1 for `fieldEndDate`
     * @param {Date[]} values The current values of `fieldStartDate` and `fieldEndDate`
     * @returns {Date}
     * @internal
     */
    defaultDate(index, values) {
      const { minDate, maxDate } = this, today = DateHelper.clearTime(/* @__PURE__ */ new Date());
      return today < minDate || today > maxDate ? minDate : today;
    }
    hasChanged(oldValue, newValue) {
      return !ObjectHelper.isEqual(oldValue, newValue);
    }
    onDateFieldChange({ source, userAction }) {
      const me = this, { fieldEndDate, fieldStartDate, _isUserAction } = me;
      me._isUserAction = userAction || _isUserAction;
      me.syncValue(source.ref, [fieldStartDate.value, fieldEndDate.value]);
      me._isUserAction = _isUserAction;
    }
    onStartDateFocusIn() {
      this.pickingStartDate = true;
    }
    onStartDateFocusOut(info) {
      this.pickingStartDate = false;
    }
    /**
     * Synchronizes the widget's state to a new `value`.
     * @param {'fieldEndDate'|'fieldStartDate'|'value'} origin The origin of the value change.
     * @param {Date[]} value
     * @internal
     */
    syncValue(origin, value2 = this.value) {
      const me = this;
      if (origin !== "fieldEndDate") {
        me.fieldEndDate.value = value2 && value2[1];
      }
      if (origin !== "fieldStartDate") {
        me.fieldStartDate.value = value2 && value2[0];
      }
      if (origin !== "value") {
        me.value = value2;
      }
      if (!me.isConstructing && me.hasChanged(me._lastValue, value2) && me.checkValid(value2)) {
        me.triggerChange();
      }
      me._lastValue = value2;
    }
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement thus.
    get widgetClass() {
    }
  }, __publicField(_a5, "configurable", {
    /**
     * One or more format for parsing date values that don't match {@link #config-format}.
     * @member {String[]}
     * @default
     * @readonly
     * @internal
     */
    altFormats: ["YYYY-MM-DD"],
    /**
     * The default configuration for {@link #config-fieldStartDate} and {@link #config-fieldEndDate}.
     * @config {DateFieldConfig}
     */
    dateFieldDefaults: {
      type: "datefield",
      autoSelect: true,
      picker: null,
      step: "1d",
      internalListeners: {
        change: "up.onDateFieldChange"
      },
      triggers: {
        expand: null,
        back: {
          align: "end",
          weight: 101
        }
      }
    },
    /**
     * The field for the end date.
     * @prp {DateField}
     * @accepts {DateFieldConfig}
     */
    fieldEndDate: {
      ref: "fieldEndDate",
      cls: "b-end-date",
      weight: 40
    },
    /**
     * The field for the start date.
     * @prp {DateField}
     * @accepts {DateFieldConfig}
     */
    fieldStartDate: {
      ref: "fieldStartDate",
      cls: "b-start-date",
      weight: 30,
      internalListeners: {
        focusIn: "up.onStartDateFocusIn",
        focusOut: "up.onStartDateFocusOut"
      }
    },
    /**
     * Specifies which date fields ({@link #config-fieldStartDate} or {@link #config-fieldEndDate}) should display
     * their forward/backward date step triggers. See {@link Core.widget.DateField#config-stepTriggers}.
     * @config {Boolean|'both'|'end'|'start'}
     * @default false
     */
    dateStepTriggers: null,
    /**
     * Get / set format for date displayed in field (see {@link Core.helper.DateHelper#function-format-static}
     * for formatting options).
     * @member {String} format
     */
    /**
     * Format for date displayed in the {@link #config-fieldStartDate} and {@link #config-fieldEndDate}.
     * @config {String}
     * @default
     */
    format: "ddd, MMM D",
    keyMap: {
      ArrowDown: "stepDown",
      ArrowUp: "stepUp",
      "Ctrl+ArrowDown": "stepDownLarge",
      "Ctrl+ArrowUp": "stepUpLarge"
    },
    /**
     * This config is used to track when the focus is in the `fieldStartDate` widget. It is `true` in this case,
     * and `false` otherwise.
     * @readonly
     * @prp {Boolean}
     * @default false
     * @internal
     */
    pickingStartDate: null,
    stepLarge: "1 month",
    stepSmall: "7 days",
    /**
     * Set to `true` to first clear time of the field's value before comparing it to the min/max value
     * @config {Boolean}
     * @default
     * @internal
     */
    validateDateOnly: true,
    /**
     * Value, which can be a Date or a string. If a string is specified, it will be converted using the
     * specified {@link #config-format}
     * @prp {String[]|Date[]}
     * @default ["today", "today"]
     */
    value: {
      $config: {
        type: "date[]",
        formats: ["format", "altFormats"]
      }
    }
  }), __publicField(_a5, "fenced", {
    syncValue: true
  }), __publicField(_a5, "prototypeProperties", {
    _lastValue: null
  }), _a5;
};

// ../Core/lib/Core/widget/MultiDatePicker.js
var { isArray: isArray3 } = Array;
var getMonthNumber = (date2) => date2.getFullYear() * 12 + date2.getMonth();
var materialRe = /material/i;
var MultiDatePicker = class extends Carousel.mixin(Fencible_default) {
  /**
   * Returns all date picker child widgets.
   * @property {Core.widget.DatePicker[]}
   * @readonly
   * @internal
   */
  get datePickers() {
    return this.items.filter((it) => it.isDatePicker);
  }
  compose() {
    var _a5;
    const { navButtons } = this, navCls = navButtons === true ? materialRe.test(((_a5 = DomHelper.getThemeInfo()) == null ? void 0 : _a5.name) || "") ? "floating" : "inline" : navButtons;
    return {
      class: {
        "b-multidatepicker-nav-buttons": navButtons,
        [`b-multidatepicker-nav-${navCls}`]: navButtons
        // '-floating' or '-inline'
      }
    };
  }
  onInternalPaint(info) {
    if (info.firstPaint) {
      const me = this, { date: date2 } = me;
      date2 && me.unanimated(() => {
        me.scrollPos = me.posFromIndex(me.indexFromDate(date2));
      });
    }
    super.onInternalPaint(info);
  }
  applyDate(date2, startDateField, endDateField) {
    const me = this, pickingStartDate = startDateField == null ? void 0 : startDateField.containsFocus, { _isUserAction: isUserAction } = me;
    let { selection } = me, fieldFocus = pickingStartDate ? endDateField : startDateField;
    if (!selection || pickingStartDate && date2 >= selection[1]) {
      fieldFocus = endDateField;
      selection = [date2, date2];
    } else if (pickingStartDate) {
      selection = [date2, selection[1] || date2];
    } else if (date2 < selection[0]) {
      fieldFocus = endDateField;
      selection = [date2, selection[1] || date2];
    } else {
      selection = [selection[0] || date2, date2];
    }
    fieldFocus == null ? void 0 : fieldFocus.focus();
    me._isUserAction = true;
    me.selection = selection;
    me._isUserAction = isUserAction;
  }
  changeIncludeYear(val) {
    if (typeof val === "number") {
      val = [val];
    }
    return isArray3(val) ? (d) => val.includes(d.getMonth() + 1) : val;
  }
  configureDatePicker(index, me, existing) {
    const { includeYear } = me, date2 = DateHelper.add(me.baseDate, index, "month");
    let config = {
      date: date2,
      activeDate: null,
      // index ? null : new Date(),
      includeYear: includeYear(date2),
      multiSelect: me.multiSelect,
      selection: me.selection,
      weekStartDay: me.weekStartDay
    };
    if (!existing) {
      config = DatePicker.mergeConfigs(me.datePickerDefaults, config, {
        cellRenderer: me.cellRenderer,
        headerRenderer: me.headerRenderer,
        weekRenderer: me.weekRenderer
      });
    }
    return config;
  }
  /**
   * Ensures that the given slot `date` is visible, scrolling if necessary to make it so.
   * @param {Object} options Options to configure which date should be visible and how to scroll if necessary.
   * @param {Date} [options.date] The date to make visible.
   * @param {Number} [options.index] The index of the slot (i.e., the month offset). This property is used by the
   * base-class (`Carousel`) but should not be used for this class. Instead, use `date`.
   * @param {Boolean} [options.animation] Pass `false` to disable scroll animation.
   * @method ensureVisible
   */
  /**
   * Decodes the `options` passed to {@link #function-ensureVisible} and ensures the object has the properties needed
   * by {@link Core.widget.Carousel#function-ensureVisible}.
   * @param {Object} options Options to configure which date should be visible and how to scroll if necessary.
   * @param {Date} [options.date] The date to make visible.
   * @param {Number} [options.index] The index of the slot (i.e., the month offset). This property is used by the
   * base-class (`Carousel`) but should not be used for this class. Instead, use `date`.
   * @param {Boolean} [options.animation] Pass `false` to disable scroll animation.
   * @internal
   */
  ensurePlan(options) {
    if (DateHelper.isDate(options)) {
      options = {
        date: options
      };
    }
    if (options.date) {
      options = {
        ...options,
        index: this.indexFromDate(options.date)
      };
      delete options.date;
    }
    return options;
  }
  getCell(date2) {
    if (!(typeof date2 === "string")) {
      date2 = DateHelper.makeKey(date2);
    }
    return this.element.querySelector(`[data-date="${date2}"]:not(.b-other-month)`);
  }
  indexFromDate(date2) {
    return date2 ? getMonthNumber(date2) - getMonthNumber(this.baseDate) : date2;
  }
  onDatePickerSelectionChange({ selection, userAction }) {
    if (userAction) {
      const me = this, { _isUserAction: isUserAction } = me;
      me._isUserAction = true;
      me.syncSelection("picker", selection);
      me._isUserAction = isUserAction;
    }
  }
  onNextMonthClick() {
    this.forward();
  }
  onPrevMonthClick() {
    this.backward();
  }
  onYearPickedMulti({ source, value: value2 }) {
    const me = this, datePicker = source.up("datepicker"), { focusableElement } = datePicker;
    focusableElement == null ? void 0 : focusableElement.focus({ preventScroll: true });
    source.hide();
    value2 -= datePicker.activeDate.getFullYear();
    me.go(value2 * 12);
  }
  // atMax
  updateAtMax(atMax, was) {
    const { nextButton, prevButton } = this.widgetMap;
    super.updateAtMax(atMax, was);
    if (nextButton) {
      nextButton.containsFocus && (prevButton == null ? void 0 : prevButton.focus());
      nextButton.isTabbable = !atMax;
      nextButton.hidden = atMax;
    }
  }
  // atMin
  updateAtMin(atMin, was) {
    const { nextButton, prevButton } = this.widgetMap;
    super.updateAtMin(atMin, was);
    if (prevButton) {
      prevButton.containsFocus && (nextButton == null ? void 0 : nextButton.focus());
      prevButton.isTabbable = !atMin;
      prevButton.hidden = atMin;
    }
  }
  // date
  changeDate(date2) {
    return date2 && DateHelper.clamp(date2, this.minDate, this.maxDate);
  }
  updateDate(date2) {
    if (date2 != null) {
      this.currentIndex = this.indexFromDate(date2);
    }
  }
  // maxDate
  updateMaxDate() {
    this.syncDateRange();
  }
  // minDate
  updateMinDate() {
    this.syncDateRange();
  }
  syncDateRange() {
    this.range = [this.minDate, this.maxDate];
  }
  // navButtons
  updateNavButtons(navButtons) {
    this.detachListeners("navButtons");
    navButtons === true && GlobalEvents_default.ion({
      theme: () => this.recompose(),
      name: "navButtons",
      thisObj: this
    });
  }
  // range
  changeRange(range, was) {
    if (isArray3(range)) {
      let [min4, max3] = range, dates;
      if (DateHelper.isDate(min4)) {
        min4 = this.indexFromDate(min4);
        dates = true;
      }
      if (DateHelper.isDate(max3)) {
        max3 = this.indexFromDate(max3);
        dates = true;
      }
      if (dates) {
        range = [min4, max3];
      }
    }
    return super.changeRange(range, was);
  }
  // selection
  changeSelection(selection, was) {
    if (selection && ObjectHelper.isEqual(selection, was)) {
      selection = was;
    }
    return selection;
  }
  updateSelection(selection) {
    const me = this;
    me.syncSelection("selection", selection);
    if (!me.isConfiguring) {
      me.trigger("selectionChange", {
        selection,
        userAction: me._isUserAction
      });
    } else if (selection) {
      me.date = selection[0];
    }
  }
  /**
   * Synchronizes the `selection` config with each date pickers' selection.
   * @param {'picker'|'selection'} origin The origin of the change in selection
   * @param {Date[]} selection The new selection
   * @internal
   */
  syncSelection(origin, selection = this.selection) {
    const { datePickers } = this;
    if (origin !== "selection") {
      this.selection = selection;
    }
    if (origin !== "picker") {
      datePickers.forEach((dp) => dp.selection = selection);
    }
  }
};
__publicField(MultiDatePicker, "$name", "MultiDatePicker");
__publicField(MultiDatePicker, "type", "multidatepicker");
__publicField(MultiDatePicker, "configurable", {
  configureSlot: "configureDatePicker",
  disableReserveSlots: "inert",
  slots: 2,
  /**
   * The date that corresponds to carousel slot index 0.
   * @config {Date}
   * @internal
   * @readonly
   */
  baseDate: {
    $config: "day",
    value: "today"
  },
  /**
   * A function (or the name of a function) which creates content in, and may mutate a day cell element.
   *
   * See {@link Core.widget.CalendarPanel#config-cellRenderer}.
   *
   * @config {Function|String}
   * @param {Object} renderData
   * @param {HTMLElement} renderData.cell The header element
   * @param {Date} renderData.date The date for the cell
   * @param {Number} renderData.day The day for the cell (`0` to `6` for Sunday to Saturday)
   * @param {Number[]} renderData.rowIndex The row index, 0 to month row count
   * @param {HTMLElement} renderData.row The row element encapsulating the week which the cell is a part of
   * @param {Core.widget.CalendarPanel} renderData.source The widget being rendered
   * @param {Number[]} renderData.cellIndex The cell index in the whole panel. May be from `0` to up to `42`
   * @param {Number[]} renderData.columnIndex The column index, `0` to `6`
   * @param {Number[]} renderData.visibleColumnIndex The visible column index taking hidden non-working days into
   * account
   * @returns {String|DomConfig|null}
   */
  cellRenderer: null,
  /**
   * A function (or the name of a function) which creates content in, and may mutate a day header element.
   *
   * See {@link Core.widget.CalendarPanel#config-headerRenderer}.
   *
   * @config {Function|String}
   * @param {Object} renderData
   * @param {HTMLElement} renderData.cell The header element
   * @param {Number} renderData.day The day number conforming to the specified {@link #config-weekStartDay}. Will
   * be in the range `0` to `6`
   * @param {Number} renderData.weekDay The canonical day number where Monday is 0 and Sunday is
   * @returns {String|DomConfig|null}
   */
  headerRenderer: null,
  /**
   * A function (or the name of a function) which creates content in, and may mutate the week cell element at the
   * start of a week row.
   *
   * See {@link Core.widget.CalendarPanel#config-weekRenderer}.
   *
   * @config {Function|String}
   * @param {Object} renderData
   * @param {HTMLElement} renderData.cell The header element
   * @param {Number[]} renderData.week An array containing `[year, weekNumber]`
   * @returns {String|DomConfig|null}
   */
  weekRenderer: null,
  /**
   * The week start day, 0 meaning Sunday, 6 meaning Saturday.
   * Defaults to {@link Core.helper.DateHelper#property-weekStartDay-static}.
   * @config {Number}
   */
  weekStartDay: null,
  /**
   * The initially selected date.
   * @config {Date}
   */
  date: {
    $config: "day",
    value: "today"
  },
  /**
   * The minimum selectable date. Selection of and navigation to dates prior
   * to this date will not be possible.
   * @config {Date}
   */
  minDate: {
    $config: "day"
  },
  /**
   * The maximum selectable date. Selection of and navigation to dates after
   * this date will not be possible.
   * @config {Date}
   */
  maxDate: {
    $config: "day"
  },
  /**
   * The configuration defaults for all date pickers in the carousel.
   * @config {DatePickerConfig}
   * @internal
   */
  datePickerDefaults: {
    type: "datepicker",
    activeDate: null,
    animateTimeShift: false,
    dayNameFormat: "d1",
    multiSelect: "range",
    trapFocus: false,
    internalListeners: {
      selectionChange: "up.onDatePickerSelectionChange"
    },
    tbar: {
      items: {
        prevMonth: {
          onAction: "up.onPrevMonthClick"
        },
        nextMonth: {
          onAction: "up.onNextMonthClick"
        },
        fields: {
          items: {
            monthField: {
              disabled: true
            },
            yearButton: {
              disabled: true
            }
          }
        }
      }
    },
    yearPicker: {
      internalListeners: {
        select: "up.onYearPickedMulti"
      }
    }
  },
  /**
   * Set to `false` to hide the year indicator field, or a function that accepts a `Date` and returns the desired
   * visibility of the year field (`true` or `false`).
   * @config {Function}
   * @param {Date} date The `Date` to evaluate when `includeYear` is a function
   * @returns {Boolean} Return `true` to include the year indicator, `false` to hide it.
   * @default
   * @internal
   */
  includeYear: (date2) => !date2.getMonth(),
  items: {
    prevButton: {
      type: "button",
      positionable: "before",
      cls: "b-multidatepicker-nav-button b-multidatepicker-prev-button b-icon b-icon-previous",
      onAction: "up.onPrevMonthClick",
      hideMode: "opacity"
    },
    nextButton: {
      type: "button",
      positionable: "after",
      cls: "b-multidatepicker-nav-button b-multidatepicker-next-button b-icon b-icon-next",
      onAction: "up.onNextMonthClick",
      hideMode: "opacity"
    }
  },
  /**
   * Configure as `true` to enable selecting multiple discontiguous date ranges using click and Shift+click to
   * create ranges and Ctrl+click to select/deselect individual dates.
   *
   * Configure as `'range'` to enable selecting a single date range by selecting a start and end date. Hold
   * "SHIFT" button to select date range. Ctrl+click may add or remove dates to/from either end of the range.
   *
   * Set to `false` to not allow multiple date selection.
   * @config {Boolean|'range'}
   * @default false
   */
  multiSelect: null,
  /**
   * Set to `false` to hide the next and previous month buttons.
   * @config {Boolean|'inline'|'floating'}
   * @default
   */
  navButtons: true,
  /**
   * The current date range selection as an array of two `Date` objects.
   * @prp {Date[]}
   */
  selection: {
    $config: "day[]"
  }
});
__publicField(MultiDatePicker, "fenced", {
  syncSelection: true
});
MultiDatePicker.initClass();
MultiDatePicker._$name = "MultiDatePicker";

// ../Core/lib/Core/widget/DateRangePicker.js
var DateRangePicker = class extends MultiDatePicker.mixin(AbstractDateRange_default) {
  get dateFieldContainer() {
    return this.tbar;
  }
  // confirmable
  changeConfirmable(confirmable) {
    this.bbar = confirmable;
    return this.bbar;
  }
  updateConfirmable(confirmable) {
    this.detachListeners("confirmable");
    confirmable == null ? void 0 : confirmable.ion({
      name: "confirmable",
      thisObj: this,
      choice: "doConfirm"
    });
  }
  //---------------------------------------------------------------------------------------------------------
  get isValid() {
    return this.checkValid();
  }
  configureDatePicker(index, me, existing) {
    const config = super.configureDatePicker(index, me, existing);
    config.selection = this.selection;
    return config;
  }
  onDatePickerBeforeDateSelect({ date: date2 }) {
    this.applyDate(date2, this.fieldStartDate, this.fieldEndDate);
    return false;
  }
  doConfirm({ choice, event, userAction }) {
    this.trigger("confirm", {
      choice,
      event,
      userAction
    });
  }
  internalOnKeyDown(event) {
    const { confirmable } = this;
    let choice = confirmable == null ? void 0 : confirmable.hotKeys[event.virtualKey];
    if (choice) {
      if (choice === "default") {
        choice = confirmable.defaultButton;
      }
      choice && this.doConfirm({
        event,
        choice: {
          [choice]: true,
          name: choice
        }
      });
    } else {
      super.internalOnKeyDown(event);
    }
  }
  /**
   * Synchronizes the `selection` and `value` configs with each date pickers' selection.
   * @param {'picker'|'selection'|'value'} origin The origin of the change in selection
   * @param {Date[]} selection The new selection
   * @internal
   */
  syncSelection(origin, selection = this.selection) {
    super.syncSelection(origin, selection);
    if (origin !== "value") {
      this.syncValue("selection", selection);
    }
  }
  /**
   * Synchronizes the widget's state to a new `value`.
   * @param {'fieldEndDate'|'fieldStartDate'|'selection'|'value'} origin The origin of the value change.
   * @param {Date[]} value
   * @internal
   */
  syncValue(origin, value2 = this.value) {
    const me = this, denormalized = (value2 == null ? void 0 : value2[1]) < (value2 == null ? void 0 : value2[0]);
    let ensureVisible;
    if (origin === "fieldEndDate") {
      ensureVisible = 2;
      if (denormalized) {
        value2 = [value2[1], value2[1]];
      }
    } else if (origin === "fieldStartDate") {
      ensureVisible = 1;
      if (denormalized) {
        value2 = [value2[0], value2[0]];
      }
    }
    if (ensureVisible && value2 && !me.isConstructing) {
      me.ensureVisible(value2[ensureVisible - 1]);
    }
    if (origin !== "selection") {
      me.syncSelection("value", value2);
    }
    super.syncValue(origin, value2);
  }
  triggerChange(event, userAction = Boolean(this._isUserAction)) {
    const { value: value2, _lastValue: oldValue, isValid: valid } = this;
    this.triggerFieldChange({ value: value2, oldValue, event, userAction, valid });
  }
};
__publicField(DateRangePicker, "$name", "DateRangePicker");
__publicField(DateRangePicker, "type", "daterangepicker");
__publicField(DateRangePicker, "configurable", {
  /**
   * @hideconfigs multiSelect, selection
   */
  /**
   * Enables OK/Cancel button bar to accept date range changes. A value of `true` shows the OK and Cancel buttons.
   * @prp {Core.widget.ConfirmationBar}
   * @accepts {Boolean|ConfirmationBarConfig}
   */
  confirmable: null,
  datePickerDefaults: {
    // focus is never on the date pickers, but only on fieldStartDate/fieldEndDate or one of the buttons
    focusable: false,
    internalListeners: {
      beforeDateSelect: "up.onDatePickerBeforeDateSelect"
    }
  },
  keyMap: {
    // when focus is not on the buttons, this widget will process Enter/Escape keys by delegating them to the
    // ConfirmationBar's keyMap:
    delegate: "confirmable"
  },
  /**
   * Unlike its base class {@link Core.widget.MultiDatePicker}, this config must be set to `'range'`.
   * @config {'range'}
   * @default
   */
  multiSelect: "range",
  /**
   * The `selection` defined by {@link Core.widget.MultiDatePicker#config-selection} and the `value` defined by
   * {@link Core.widget.mixin.AbstractDateRange#config-value} are synchronized. Use of `value` is preferred since
   * this widget is used together with {@link Core.widget.DateRangeField} which also uses `value`.
   * @config {Date[]|String[]}
   * @default
   */
  selection: ["today", "today"],
  stripDefaults: {
    bbar: {
      type: "confirmationbar"
    }
  },
  tbar: {
    items: {
      spacer: {
        type: "widget",
        flex: 1,
        weight: 0
      }
    }
  }
});
DateRangePicker.initClass();
DateRangePicker._$name = "DateRangePicker";

// ../Core/lib/Core/widget/DateRangeField.js
var TODAY2 = /* @__PURE__ */ new Date();
var DateRangeField = class extends PickerField.mixin(AbstractDateRange_default) {
  //endregion
  //region Composition
  get dateFieldContainer() {
    return this.container;
  }
  get innerElements() {
    return [];
  }
  // DateField has min/max configs while DatePicker has minDate/maxDate... since we want to mimic the interface
  // of DateField as closely as possible, we add these getters to allow AbstractDateRange to access the valid
  // range using a common interface.
  get maxDate() {
    return this.max;
  }
  get minDate() {
    return this.min;
  }
  compose() {
    const { pickTime } = this;
    return {
      class: {
        "b-pick-time": Boolean(pickTime)
      }
    };
  }
  //endregion
  //region Config processing
  // clearable
  updateClearable(clearable) {
    const { fieldEndDate } = this;
    fieldEndDate.clearable = clearable;
    if (clearable) {
      fieldEndDate.triggers.clear.handler = () => this.clear();
    }
  }
  // confirmable
  updateConfirmable() {
    if (!this.isConstructing) {
      this.picker = this._pickerConfig;
    }
  }
  // fieldEndTime
  changeEndTimeField(config, existing) {
    return TimeField.reconfigure(existing, config, {
      owner: this,
      defaults: TimeField.mergeConfigs(this.timeFieldDefaults, this.fieldEndTimeDefaults)
    });
  }
  updateEndTimeField(field) {
    field && this.container.add(field);
  }
  // fieldStartTime
  changeStartTimeField(config, existing) {
    return TimeField.reconfigure(existing, config, {
      owner: this,
      defaults: TimeField.mergeConfigs(this.timeFieldDefaults, this.fieldStartTimeDefaults)
    });
  }
  updateStartTimeField(field) {
    field && this.container.add(field);
  }
  // keepTime
  updateKeepTime(keepTime) {
    keepTime = !this.pickTime && keepTime;
    this.fieldEndDate.keepTime = keepTime;
    this.fieldStartDate.keepTime = keepTime;
  }
  // max
  updateMax(max3) {
    const { fieldEndDate, fieldStartDate, _picker: picker } = this;
    fieldEndDate.max = max3;
    fieldStartDate.max = max3;
    if (picker) {
      picker.maxDate = max3;
    }
    this.syncInvalid();
  }
  // min
  updateMin(min4) {
    const { fieldEndDate, fieldStartDate, _picker: picker } = this;
    fieldEndDate.min = min4;
    fieldStartDate.min = min4;
    if (picker) {
      picker.minDate = min4;
    }
    this.syncInvalid();
  }
  // picker
  changePicker(config, existing) {
    const me = this, { confirmable, pickerAlignElement } = me, pickerDefaults = confirmable ? me.confirmableDefaults : me.nonConfirmableDefaults, pickerClass = Widget.resolveType(pickerDefaults.type), defaults = pickerClass.mergeConfigs({
      owner: me,
      forElement: pickerAlignElement,
      minDate: me.min,
      maxDate: me.max,
      selection: me.value,
      weekStartDay: me._weekStartDay,
      // need to pass the raw value to let the component to use its default value
      align: {
        anchor: me.overlayAnchor,
        target: pickerAlignElement
      }
    }, pickerDefaults, confirmable && { confirmable });
    me._pickerConfig = config;
    if (ObjectHelper.isObject(config)) {
      config = ObjectHelper.merge({ type: defaults.type }, config);
    }
    return pickerClass.reconfigure(existing, config, {
      owner: me,
      defaults
    });
  }
  updatePicker(picker, was) {
    super.updatePicker(picker, was);
    this.detachListeners("dateRangeField.picker");
    picker == null ? void 0 : picker.ion({
      name: "dateRangeField.picker",
      thisObj: this,
      selectionChange: "onPickerSelectionChange",
      confirm: "onPickerConfirm",
      focusIn: "onPickerFocusIn"
    });
  }
  // pickerCls
  updatePickerCls(cls, was) {
    var _a5, _b2;
    const classList = (_b2 = (_a5 = this.picker) == null ? void 0 : _a5.element) == null ? void 0 : _b2.classList;
    was && (classList == null ? void 0 : classList.remove(was));
    cls && (classList == null ? void 0 : classList.add(cls));
  }
  // pickTime
  updatePickTime(config) {
    let fieldEndTime = null, fieldStartTime = null;
    if (config === true) {
      fieldEndTime = fieldStartTime = {};
    } else if (config) {
      if (config.fieldStartTime || config.fieldEndTime) {
        fieldStartTime = config.fieldStartTime;
        fieldEndTime = config.fieldEndTime;
      } else {
        fieldStartTime = fieldEndTime = config;
      }
    }
    this.fieldEndTime = fieldEndTime;
    this.fieldStartTime = fieldStartTime;
  }
  // value
  changeValue(value2) {
    const me = this, invalidDateStr = me.L("L{DateField.invalidDate}")(DateHelper.format(TODAY2, me.format));
    if (value2 && !(DateHelper.isDate(value2[0]) && DateHelper.isDate(value2[1]))) {
      me.setError(invalidDateStr);
      return;
    }
    me.clearError(invalidDateStr);
    return value2;
  }
  // weekStartDay
  get weekStartDay() {
    return typeof this._weekStartDay === "number" ? this._weekStartDay : DateHelper.weekStartDay;
  }
  updateWeekStartDay(weekStartDay) {
    if (this._picker) {
      this._picker.weekStartDay = weekStartDay;
    }
  }
  //endregion
  //region Event handlers
  maybeHidePicker() {
    if (!this.picker.isVisible || this.confirmable) {
      return false;
    }
    this.hidePicker();
  }
  onDatePickerBeforeDateSelect({ date: date2 }) {
    this.picker.applyDate(date2, this.fieldStartDate, this.fieldEndDate);
    return false;
  }
  onDateFieldChange({ source }) {
    const me = this, { ref } = source, changeIndex = ref === "fieldStartDate" ? 0 : 1, { pickTime } = me;
    let changedVal, otherIndex, timeField, val, value2;
    if (!me.isConstructing) {
      changedVal = source.value;
      otherIndex = 1 - changeIndex;
      val = me.value;
      if (changedVal) {
        if (pickTime) {
          timeField = changeIndex ? me.fieldStartTime : me.fieldEndTime;
          changedVal = DateHelper.combineDateAndTime(changedVal, timeField.value);
        } else {
          changedVal = DateHelper.clearTime(changedVal);
        }
        value2 = [changedVal, changedVal];
        if (val) {
          value2[otherIndex] = val[otherIndex];
          if (value2[1] < value2[0]) {
            value2[otherIndex] = DateHelper.combineDateAndTime(changedVal, value2[otherIndex]);
            if (value2[1] < value2[0]) {
              value2[otherIndex] = changedVal;
            }
          }
        }
        me.syncValue(ref, value2);
      }
    }
  }
  onKeyArrowUp(event) {
    const me = this, { picker } = me;
    if (picker.isVisible) {
      if (me.confirmable) {
        return false;
      }
      return me.stepUp(event);
    }
    me.showPicker();
  }
  onKeyClear() {
    this.clearable && this.clear();
  }
  onKeyEnter() {
    this.maybeHidePicker();
  }
  onKeyEscape() {
    this.maybeHidePicker();
  }
  onExpandPicker() {
    this.confirmable ? this.showPicker() : this.togglePicker();
  }
  onPickerSelectionChange({ selection, userAction }) {
    if (!this.confirmable) {
      const me = this, { _isUserAction } = me;
      me._isUserAction = userAction || _isUserAction;
      me.syncValue("picker", selection);
      me._isUserAction = _isUserAction;
    }
  }
  onPickerConfirm({ choice, source, userAction }) {
    const me = this, { _isUserAction } = me, field = source.containsFocus && (source.pickingStartDate ? me.fieldStartDate : me.fieldEndDate);
    if (choice.ok) {
      me._isUserAction = userAction || _isUserAction;
      me.syncValue("picker", source.value);
      me._isUserAction = _isUserAction;
    }
    me.hidePicker();
    field == null ? void 0 : field.focus();
  }
  onPickerFocusIn({ relatedTarget }) {
    const { fieldEndDate, fieldStartDate } = this.container.widgetMap, retarget = relatedTarget === fieldStartDate ? fieldStartDate : fieldEndDate;
    !this.confirmable && retarget.focus();
  }
  onTriggerKeyDown(event) {
    if (this.picker.isVisible && !this.confirmable) {
      return this.stepDown(event);
    }
    return super.onTriggerKeyDown(event);
  }
  //endregion
  //region Picker
  focusPicker() {
    const { picker } = this, { fieldEndDate, fieldStartDate } = this.container.widgetMap, { tbar } = picker, { fieldEndDate: fieldEndDatePicker, fieldStartDate: fieldStartDatePicker } = (tbar == null ? void 0 : tbar.widgetMap) || {}, target = tbar ? fieldEndDate.containsFocus && fieldEndDatePicker || fieldStartDatePicker : fieldEndDate.containsFocus && fieldEndDate || fieldStartDate;
    target.focus();
    if (target !== picker) {
      picker.ensureVisible({
        animate: false,
        date: target.value
      });
      target.selectAll();
    }
  }
  showPicker(focusPicker) {
    const me = this, { _picker: picker } = me;
    if (!me.readOnly) {
      if (picker) {
        picker.setConfig({
          range: [me.min, me.max],
          selection: me.value
        });
      }
      focusPicker = focusPicker || Boolean(me.confirmable);
      super.showPicker(focusPicker);
    }
  }
  //endregion
  // region Validation
  get isValid() {
    const me = this, check = me.checkMinMax();
    me.clearError("L{Field.minimumValueViolation}", true);
    me.clearError("L{Field.maximumValueViolation}", true);
    if (check) {
      me.setError(check < 0 ? "L{Field.minimumValueViolation}" : "L{Field.maximumValueViolation}", true);
      return false;
    }
    return super.isValid;
  }
  //endregion
  //region Other
  setupLabel(lbl) {
    return super.setupLabel(ObjectHelper.assign({
      for: this.fieldStartDate.input.id
    }, lbl));
  }
  syncInputFieldValue() {
  }
  // we have no input field
  syncValue(origin, value2 = this.value) {
    const me = this, { _picker: picker } = me, denormalized = (value2 == null ? void 0 : value2[1]) < (value2 == null ? void 0 : value2[0]);
    if ((picker == null ? void 0 : picker.isVisible) && !me.confirmable) {
      let ensureVisible;
      if (origin === "fieldEndDate") {
        ensureVisible = 2;
        if (denormalized) {
          value2 = [value2[1], value2[1]];
        }
      } else if (origin === "fieldStartDate") {
        ensureVisible = 1;
        if (denormalized) {
          value2 = [value2[0], value2[0]];
        }
      }
      if (ensureVisible && value2 && !me.isConstructing) {
        picker.ensureVisible(value2[ensureVisible - 1]);
      }
    }
    if (origin !== "picker" && picker) {
      picker.selection = value2;
    }
    super.syncValue(origin, value2);
  }
  //endregion
};
//region Config
__publicField(DateRangeField, "$name", "DateRangeField");
// Factoryable type name
__publicField(DateRangeField, "type", "daterangefield");
// Factoryable type alias
__publicField(DateRangeField, "alias", "daterange");
__publicField(DateRangeField, "configurable", {
  inline: true,
  /**
   * Enables OK/Cancel button bar to accept date range changes. A value of `true` shows the OK and Cancel buttons.
   *
   * See {@link Core.widget.DateRangePicker#config-confirmable}
   * @prp {Core.widget.ConfirmationBar}
   * @accepts {Boolean|ConfirmationBarConfig}
   */
  confirmable: null,
  container: {
    // items are added dynamically
  },
  confirmableDefaults: {
    type: "daterangepicker",
    navButtons: "floating",
    trapFocus: true,
    align: {
      align: "t100-t100",
      offset: '[data-ref="fieldEndDate"]',
      overlap: true
    },
    fieldStartDate: {
      triggers: {
        expand: {
          align: "start",
          disabled: true
        }
      }
    }
  },
  fieldEndDate: {
    internalListeners: {
      triggerKey: "up.onTriggerKey"
    },
    triggers: {
      expand: null
    }
  },
  fieldEndTime: null,
  fieldEndTimeDefaults: {
    ref: "fieldEndTime",
    cls: "b-end-time",
    weight: 20
  },
  fieldStartDate: {
    internalListeners: {
      triggerKey: "up.onTriggerKey"
    },
    triggers: {
      expand: {
        handler: "up.onExpandPicker",
        align: "start"
      }
    }
  },
  fieldStartTime: null,
  fieldStartTimeDefaults: {
    ref: "fieldStartTime",
    cls: "b-start-time",
    weight: 10
  },
  /**
   * A flag which indicates what time should be used for selected date.
   * `false` by default which means time is reset to midnight.
   *
   * Possible options are:
   * - `false` to reset time to midnight
   * - `true` to keep original time value
   * - `'17:00'` a string which is parsed automatically
   * - `new Date(2020, 0, 1, 17)` a date object to copy time from
   * - `'entered'` to keep time value entered by user (in case {@link #config-format} includes time info)
   *
   * @config {Boolean|Date|String}
   * @default false
   */
  keepTime: null,
  keyMap: {
    ArrowDown: "onTriggerKeyDown",
    ArrowUp: "onKeyArrowUp",
    Enter: "onKeyEnter",
    Escape: "onKeyEscape",
    "Shift+Backspace": "onKeyClear"
  },
  /**
   * Get/set max value, which can be a Date or a string. If a string is specified, it will be converted using
   * the specified {@link #config-format}.
   * @member {Date} max
   * @accepts {String|Date}
   */
  /**
   * Max value
   * @config {String|Date}
   */
  max: {
    $config: {
      type: "date",
      formats: ["format", "altFormats"],
      strict: true
    }
  },
  /**
   * Get/set min value, which can be a Date or a string. If a string is specified, it will be converted using
   * the specified {@link #config-format}.
   * @member {Date} min
   * @accepts {String|Date}
   */
  /**
   * Min value
   * @config {String|Date}
   */
  min: {
    $config: {
      type: "date",
      formats: ["format", "altFormats"],
      strict: true
    }
  },
  nonConfirmableDefaults: {
    type: "multidatepicker",
    align: {
      align: "t100-r100"
    },
    datePickerDefaults: {
      // focus is never on the date pickers
      focusable: false,
      internalListeners: {
        beforeDateSelect: "up.onDatePickerBeforeDateSelect"
      }
    }
  },
  /**
   * A config object used to configure the {@link Core.widget.DateRangePicker datePicker}.
   * ```javascript
   * dateField = new DateRangeField({
   *      picker    : {
   *          multiSelect : true
   *      }
   *  });
   * ```
   * @config {DateRangePickerConfig|MultiDatePickerConfig}
   */
  picker: {
    cls: "b-daterangefield-picker",
    floating: true,
    hideMode: "clip",
    multiSelect: "range",
    scrollAction: "realign",
    align: {
      axisLock: true
    }
  },
  pickerAlignElement: "container.element",
  /**
   * An optional extra CSS class to add to the picker container element.
   * @prp {String}
   * @internal
   */
  pickerCls: null,
  /**
   * Set to `true` to include time fields to allow the user to pick the time of day. When this config is
   * set, `keepTime` is no longer used.
   * @config {Boolean}
   * @default false
   * @internal
   */
  pickTime: null,
  stepSmall: "7 days",
  timeFormat: "HH:mm:ss:SSS",
  triggers: {
    expand: null
  },
  /**
   * The week start day in the {@link #config-picker}, 0 meaning Sunday, 6 meaning Saturday.
   * Uses localized value per default.
   * @config {Number}
   */
  weekStartDay: null,
  timeFieldDefaults: {
    type: "timefield",
    autoSelect: true,
    picker: {
      align: {
        align: "b0-t0"
      }
    },
    triggers: {
      expand: null
    }
  }
});
DateRangeField.initClass();
DateRangeField._$name = "DateRangeField";

// ../Core/lib/Core/widget/DateTimeField.js
var midnightDate = new Date(2e3, 0, 1);
var DateTimeField = class extends Field {
  doDestroy() {
    this.dateField.destroy();
    this.timeField.destroy();
    super.doDestroy();
  }
  get childItems() {
    return [this.dateField, this.timeField];
  }
  get fieldDefaults() {
    const { revertOnEscape, tabIndex } = this;
    return tabIndex == null ? {
      revertOnEscape
    } : {
      revertOnEscape,
      tabIndex
    };
  }
  get focusElement() {
    return this.dateField.input;
  }
  // Implementation needed at this level because it has two inner elements in its inputWrap
  get innerElements() {
    return [
      this.dateField.element,
      this.timeField.element
    ];
  }
  // Each subfield handles its own keystrokes
  internalOnKeyEvent() {
  }
  // CellEdit sets this dynamically on its editor field
  updateRevertOnEscape(revertOnEscape) {
    this.timeField.revertOnEscape = revertOnEscape;
    this.dateField.revertOnEscape = revertOnEscape;
  }
  changeTimeField(config, existing) {
    return TimeField.reconfigure(existing, config, {
      owner: this,
      defaults: this.fieldDefaults
    });
  }
  updateTimeField(timeField) {
    const me = this;
    EventHelper.on({
      element: timeField.element,
      keydown: "onTimeFieldKeyDown",
      thisObj: me
    });
    FunctionHelper.after(timeField, "syncInvalid", () => {
      me.timeField && !me.updatingInvalid && me.syncInvalid();
    });
    timeField.ion({
      thisObj: me,
      change({ userAction, value: value2 }) {
        if (userAction && !me.$settingValue) {
          const dateAndTime = me.dateField.value;
          me._isUserAction = true;
          me.value = dateAndTime ? DateHelper.copyTimeValues(new Date(dateAndTime), value2 || midnightDate) : null;
          me._isUserAction = false;
        }
      }
    });
    if (me.readOnly) {
      timeField.readOnly = true;
    }
  }
  changeDateField(config, existing) {
    return DateField.reconfigure(existing, config, {
      owner: this,
      defaults: this.fieldDefaults
    });
  }
  updateDateField(dateField) {
    const me = this;
    EventHelper.on({
      element: dateField.element,
      keydown: "onDateFieldKeyDown",
      thisObj: me
    });
    FunctionHelper.after(dateField, "syncInvalid", () => {
      me.dateField && !me.updatingInvalid && me.syncInvalid();
    });
    dateField.ion({
      thisObj: me,
      change({ userAction, value: value2 }) {
        if (userAction && !me.$isInternalChange) {
          me._isUserAction = true;
          me.timeField.value = value2;
          me.value = value2;
          me._isUserAction = false;
        }
      },
      keydown({ event }) {
        var _a5;
        if (event.key === "Tab" && !event.shiftKey && ((_a5 = me.timeField) == null ? void 0 : _a5.isVisible)) {
          event.stopPropagation();
        }
      }
    });
    if (me.readOnly) {
      dateField.readOnly = true;
    }
  }
  updateWeekStartDay(weekStartDay) {
    if (this.dateField) {
      this.dateField.weekStartDay = weekStartDay;
    }
  }
  changeWeekStartDay(value2) {
    var _a5, _b2;
    return typeof value2 === "number" ? value2 : (_b2 = (_a5 = this.dateField) == null ? void 0 : _a5.weekStartDay) != null ? _b2 : DateHelper.weekStartDay;
  }
  // Apply our value to our underlying fields
  syncInputFieldValue(skipHighlight = this.isConfiguring) {
    super.syncInputFieldValue(true);
    const me = this, { dateField, timeField } = me, highlightDate = dateField.highlightExternalChange, highlightTime = timeField.highlightExternalChange;
    if (!skipHighlight && !me.highlightExternalChange) {
      skipHighlight = true;
    }
    me.$isInternalChange = true;
    dateField.highlightExternalChange = false;
    dateField.highlightExternalChange = highlightDate;
    if (skipHighlight) {
      timeField.highlightExternalChange = dateField.highlightExternalChange = false;
    }
    timeField.value = dateField.value = me.inputValue;
    dateField.highlightExternalChange = highlightDate;
    timeField.highlightExternalChange = highlightTime;
    me.$isInternalChange = false;
    me.syncInvalid();
  }
  onTimeFieldKeyDown(e) {
    const me = this;
    if (!me.readOnly && (e.key === "Enter" || e.key === "Tab")) {
      const dateAndTime = me.dateField.value;
      me._isUserAction = true;
      me.value = dateAndTime ? DateHelper.copyTimeValues(new Date(dateAndTime), me.timeField.value || midnightDate) : null;
      me._isUserAction = false;
    }
  }
  onDateFieldKeyDown(e) {
    const me = this;
    if (e.key === "Tab" && !e.shiftKey) {
      e.stopPropagation();
      e.preventDefault();
      me.timeField.focus();
    } else if (!me.readOnly && e.key === "Enter") {
      me.value = me.dateField.value;
    }
  }
  // Make us and our underlying fields required
  updateRequired(required) {
    super.updateRequired(required);
    this.timeField.required = this.dateField.required = required;
  }
  updateReadOnly(readOnly, was) {
    super.updateReadOnly(readOnly, was);
    if (!this.isConfiguring) {
      this.timeField.readOnly = this.dateField.readOnly = readOnly;
    }
  }
  // Make us and our underlying fields disabled
  onDisabled(value2) {
    this.timeField.disabled = this.dateField.disabled = value2;
  }
  focus() {
    this.dateField.focus();
  }
  hasChanged(oldValue, newValue) {
    var _a5, _b2;
    if ((oldValue == null ? void 0 : oldValue.getTime) && (newValue == null ? void 0 : newValue.getTime)) {
      return !DateHelper.isEqual(oldValue, newValue);
    }
    return super.hasChanged((_a5 = oldValue == null ? void 0 : oldValue.getTime) == null ? void 0 : _a5.call(oldValue), (_b2 = newValue == null ? void 0 : newValue.getTime) == null ? void 0 : _b2.call(newValue));
  }
  get isValid() {
    return this.timeField.isValid && this.dateField.isValid;
  }
  setError(error, silent) {
    [this.dateField, this.timeField].forEach((f) => f.setError(error, silent));
  }
  getErrors() {
    const errors = [...this.dateField.getErrors() || [], ...this.timeField.getErrors() || []];
    return errors.length ? errors : null;
  }
  clearError(error, silent) {
    [this.dateField, this.timeField].forEach((f) => f.clearError(error, silent));
  }
  updateInvalid() {
    this.updatingInvalid = true;
    this.dateField.updateInvalid();
    this.timeField.updateInvalid();
    this.updatingInvalid = false;
  }
};
__publicField(DateTimeField, "$name", "DateTimeField");
__publicField(DateTimeField, "type", "datetimefield");
__publicField(DateTimeField, "alias", "datetime");
__publicField(DateTimeField, "configurable", {
  /**
   * Returns the TimeField instance
   * @readonly
   * @member {Core.widget.TimeField} timeField
   */
  /**
   * Configuration for the {@link Core.widget.TimeField}
   * @config {TimeFieldConfig}
   */
  timeField: {
    type: "timefield"
  },
  /**
   * Returns the DateField instance
   * @readonly
   * @member {Core.widget.DateField} dateField
   */
  /**
   * Configuration for the {@link Core.widget.DateField}
   * @config {DateFieldConfig}
   */
  dateField: {
    type: "datefield",
    // To be able to use transformDateValue for parsing without loosing time, a bit of a hack
    keepTime: true,
    step: "1 d"
  },
  /**
   * The week start day in the {@link Core.widget.DateField#config-picker}, 0 meaning Sunday, 6 meaning Saturday.
   * Uses localized value per default.
   *
   * @config {Number}
   */
  weekStartDay: null,
  inputTemplate: () => "",
  ariaElement: "element"
});
DateTimeField.initClass();
DateTimeField._$name = "DateTimeField";

// ../Core/lib/Core/widget/panel/PanelCollapserOverlay.js
var { dockIsHorz: dockIsHorz2 } = PanelCollapser.maps;
var collapseExposeEdge = {
  top: 0,
  down: 0,
  left: 1,
  bottom: 2,
  up: 2,
  right: 3
};
var translateByDir = {
  up: {
    from: `translate(0,0)`,
    to: "translate(0,-100%)"
  },
  down: {
    from: `translate(0,0)`,
    to: "translate(0,100%)"
  },
  left: {
    from: `translate(0,0)`,
    to: "translate(-100%,0)"
  },
  right: {
    from: `translate(0,0)`,
    to: "translate(100%,0)"
  }
};
var PanelCollapserOverlay = class extends PanelCollapser.mixin(Delayable_default) {
  static get configurable() {
    return {
      /**
       * The number of milliseconds to wait once the mouse leaves a {@link Core.widget.Panel#config-revealed}
       * panel before returning to an unrevealed state. Clicking outside the revealed panel will immediately
       * return the panel to its collapsed state.
       *
       * This may be disabled by configuring {@link #config-autoClose} as `null`.
       *
       * If this value is negative, the panel will not automatically recollapse due to the mouse leaving, however,
       * clicks outside the panel will still recollapse it.
       *
       * If this value is `null`, the panel will not automatically recollapse for either outside clicks or if
       * the mouse leaves the panel.
       * @config {Number}
       * @default
       */
      autoCloseDelay: 1e3,
      /**
       * By default, clicking outside the revealed overlay hides the revealed overlay.
       *
       * If the revealed overlay was shown using the {@link #property-recollapseTool}
       * then moving the mouse outside of the revealed overlay hides the revealed overlay.
       *
       * Configure this as `false` to disable auto hiding, making overlayed
       * state permanent, and changeable using the {@link #function-toggleReveal} method.
       * @config {Boolean}
       * @default
       */
      autoClose: true,
      revealing: {
        value: null,
        $config: null,
        default: false
      },
      /**
       * The reveal/hide tool which slides the collapsed panel over the top of the UI.
       * @member {Core.widget.Tool} recollapseTool
       */
      /**
       * The reveal/hide tool which slides the collapsed panel over the top of the UI.
       *
       * The `type` of this instance should not be changed but the tool instance can be
       * configured in other ways via this config property.
       * @config {ToolConfig|Core.widget.Tool}
       */
      recollapseTool: {
        type: "collapsetool",
        cls: "b-recollapse",
        collapsify: "overlay",
        handler() {
          var _a5;
          (_a5 = this.collapsible) == null ? void 0 : _a5.toggleReveal();
        }
      }
    };
  }
  static get delayable() {
    return {
      doAutoClose: 0
    };
  }
  doAutoClose() {
    this.toggleReveal(false);
  }
  updateAutoCloseDelay(delay2) {
    const { doAutoClose } = this;
    if (!(doAutoClose.suspended = delay2 == null || delay2 < 0)) {
      doAutoClose.delay = delay2;
      doAutoClose.immediate = !delay2;
    }
  }
  changeRecollapseTool(tool) {
    const me = this, { panel } = me;
    if (me.isConfiguring || me.isDestroying || !panel || panel.isDestroying) {
      return tool;
    }
    panel.tools = {
      recollapse: tool
    };
  }
  beforeCollapse(operation) {
    if (super.beforeCollapse(operation) === false) {
      return false;
    }
    if (this.panel.revealed) {
      operation.animation = null;
    }
  }
  applyHeaderDock(collapsed, flush = true) {
    var _a5;
    (_a5 = this.panel) == null ? void 0 : _a5.recompose();
    super.applyHeaderDock(collapsed, flush);
  }
  collapseBegin(operation) {
    const me = this, { collapseDir, innerElement } = me, { animation } = operation, { collapseTool, panel } = me;
    me.configuredWidth = panel._lastWidth;
    me.configuredHeight = panel._lastHeight;
    me.applyHeaderDock(true);
    me.lockInnerSize();
    collapseTool == null ? void 0 : collapseTool.element.classList.add("b-collapsed");
    if (animation) {
      panel.element.classList.add("b-collapsing");
      animation.element = innerElement;
      animation.transform = translateByDir[collapseDir];
      operation.animation = Animator.run(animation);
    }
  }
  onComplete(action) {
    var _a5, _b2;
    super.onComplete(action);
    const me = this, { panel } = me, { element } = panel;
    me.autoCloseLeaveDetacher = (_a5 = me.autoCloseLeaveDetacher) == null ? void 0 : _a5.call(me);
    me.autoCloseClickDetacher = (_b2 = me.autoCloseClickDetacher) == null ? void 0 : _b2.call(me);
    me.doAutoClose.cancel();
    if (action === "reveal" && me.autoClose) {
      if (panel.containsFocus) {
        me.autoCloseLeaveDetacher = EventHelper.on({
          element,
          mouseenter: (ev) => {
            me.doAutoClose.cancel();
          },
          mouseleave: (ev) => {
            me.doAutoClose();
          }
        });
      }
      me.autoCloseClickDetacher = EventHelper.on({
        element: document.body,
        thisObj: panel,
        mousedown: (ev) => {
          var _a6;
          if (!panel.owns(ev) && !((_a6 = me.revealer) == null ? void 0 : _a6.contains(ev.target)) && me.autoCloseDelay != null) {
            me.doAutoClose.now();
          }
        }
      });
    }
    if (!me.hasPainted && action === "collapse") {
      me.innerElement.offsetHeight;
    }
  }
  expandBegin(operation) {
    const me = this, { animation } = operation, { collapseDir, collapseTool, innerElement, panel } = me, { element } = panel;
    element.classList.remove("b-collapsed", "b-collapsing");
    me.restoreConfiguredSize();
    me.lockInnerSize(false);
    me.lockInnerSize();
    collapseTool == null ? void 0 : collapseTool.element.classList.remove("b-collapsed");
    if (animation) {
      element.classList.add("b-collapsed", "b-expanding");
      animation.element = innerElement;
      animation.transform = {
        from: translateByDir[collapseDir].to,
        to: translateByDir[collapseDir].from
      };
      operation.animation = Animator.run(animation);
    }
  }
  expandEnd(operation) {
    super.expandEnd(operation);
    const { panel } = this;
    if (operation.completed) {
      panel.revealed = false;
    }
    panel.element.classList.remove("b-expanding");
  }
  expandRevert(operation) {
    super.expandRevert(operation);
    this.panel.element.classList.add("b-expanding");
  }
  get innerElement() {
    return this.panel.overlayElement;
  }
  get innerSizeElement() {
    return this.panel.element;
  }
  get toolsConfig() {
    const me = this, { direction } = me, config = super.toolsConfig, tool = me.recollapseTool;
    if (tool) {
      return {
        ...config,
        recollapse: tool && ObjectHelper.assign({
          direction: direction.toLowerCase()
        }, tool)
      };
    }
    return config;
  }
  lockInnerSize(lock = true) {
    const me = this, { panel } = me, panelRect = lock && panel.rectangle(), inset = lock ? [0, 0, 0, 0] : "", innerRect = super.lockInnerSize(lock);
    if (lock) {
      inset[collapseExposeEdge[me.collapseDir]] = `-${panelRect[me.collapseDim] + 10}px`;
    }
    panel.element.style.clipPath = lock ? `inset(${inset.join(" ")})` : "";
    return innerRect;
  }
  onOverlayTransitionDone(ev) {
    const me = this, { panel } = me;
    if (ev.srcElement === panel.overlayElement && me.revealing) {
      me.revealing = false;
      me.onComplete(panel.revealed ? "reveal" : "unreveal");
    }
  }
  onRevealerClick() {
    this.toggleReveal();
  }
  /**
   * Toggles the revealed state of the Panel to match the passed boolean flag.
   * @param {Boolean} [state]  If not specified, this method toggles current state. Otherwise, pass `true` to reveal
   * the overlay, or `false` to hide it.
   */
  toggleReveal(state) {
    const { panel } = this, { element } = panel;
    if (panel.collapsed) {
      this.revealer = DomHelper.getActiveElement(element);
      if (state == null) {
        state = !panel.revealed;
      }
      if (panel.revealed !== state && panel.trigger("beforeToggleReveal", { reveal: state }) !== false) {
        if (!state && element.matches(":focus-within")) {
          panel.revertFocus(true);
        }
        this.revealing = true;
        panel.revealed = state;
        element.getBoundingClientRect();
      }
    }
  }
  updateRevealing(value2) {
    const me = this, horzDirRe = /left|right/i, { panel } = me, dim = horzDirRe.test(me.collapseDir) ? "height" : "width";
    if (panel) {
      me.innerElement.style[dim] = "0px";
      me.innerElement.style[`min-${dim}`] = "100%";
      panel.element.classList[value2 ? "add" : "remove"]("b-panel-overlay-revealing");
    }
  }
  wrapCollapser(key, body) {
    var _a5;
    const me = this, { collapseDir, panel } = me, { expandedHeaderDock, header, uiClassList } = panel, recollapse = (_a5 = panel.tools) == null ? void 0 : _a5.recollapse, [before, after] = me.splitHeaderItems({ as: "element", dock: me.collapseDock }), horz = dockIsHorz2[expandedHeaderDock], title = panel.hasHeader ? panel.title || (header == null ? void 0 : header.title) || "\xA0" : null;
    if (recollapse) {
      recollapse.direction = collapseDir;
    }
    return [
      "overlayElement",
      {
        class: {
          ...uiClassList,
          [`b-panel-overlay-header-${expandedHeaderDock}`]: 1,
          [`b-panel-overlay-${collapseDir}`]: 1,
          [`b-${horz ? "h" : "v"}box`]: 1,
          "b-panel-overlay": 1,
          "b-box-center": 1
        },
        // internalListeners is not correct for element listeners in domConfigs
        listeners: {
          // eslint-disable-line bryntum/no-listeners-in-lib
          transitionend: (ev) => me.onOverlayTransitionDone(ev),
          transitioncancel: (ev) => me.onOverlayTransitionDone(ev)
        },
        children: {
          overlayHeaderElement: title && {
            tag: "header",
            class: new DomClassList({
              ...uiClassList,
              [`b-dock-${expandedHeaderDock}`]: 1,
              "b-panel-header": 1,
              "b-panel-overlay-header": 1
            }, header == null ? void 0 : header.cls),
            children: [
              ...before,
              {
                reference: "overlayTitleElement",
                html: title,
                class: {
                  ...uiClassList,
                  [`b-align-${(header == null ? void 0 : header.titleAlign) || "start"}`]: 1,
                  "b-header-title": 1
                }
              },
              ...after
            ]
          },
          [key]: body
        }
      }
    ];
  }
};
__publicField(PanelCollapserOverlay, "$name", "PanelCollapserOverlay");
__publicField(PanelCollapserOverlay, "type", "overlay");
PanelCollapserOverlay.initClass();
PanelCollapserOverlay._$name = "PanelCollapserOverlay";

// ../Core/lib/Core/widget/DemoCodeEditor.js
var fileStart = {
  lineNumber: 0,
  column: 0
};
var fileEnd = {
  lineNumber: Number.MAX_SAFE_INTEGER,
  column: Number.MAX_SAFE_INTEGER
};
var isBryntumCom = BrowserHelper.isBryntumOnline(["online"]);
var { pathname } = globalThis.location;
var isUmd = pathname.endsWith("umd.html");
var moduleTag = document.querySelector("script[type=module]");
var isModule = pathname.endsWith("module.html") || (moduleTag == null ? void 0 : moduleTag.src.includes("app.module.js")) || (!pathname.includes(".html") || pathname.includes("index.html")) && isBryntumCom;
var _DemoCodeEditor = class _DemoCodeEditor extends CodeEditor {
  //endregion
  construct(config = {}) {
    super.construct(...arguments);
    const me = this, { rtl } = me;
    if (rtl) {
      me.collapsible.direction = "left";
    }
    new ResizeHelper({
      targetSelector: ".b-codeeditor",
      rightHandle: Boolean(rtl),
      leftHandle: !rtl,
      skipTranslate: true,
      minWidth: 190
    });
  }
  static get isModule() {
    return isModule;
  }
  get isVanilla() {
    return this.mode === "vanilla";
  }
  get isFramework() {
    return this.mode === "framework";
  }
  updateMode() {
    this.title = '<span class="title-container"><span class="title">' + (this.isVanilla ? "L{CodeEditor.editor}" : "L{CodeEditor.viewer}") + "</span></span>";
  }
  onCloseClick() {
    this.collapse();
  }
  async onFilesComboChange({ value: value2 }) {
    await this.loadCode(value2);
  }
  async collapseBoilerplate(model) {
    const {
      monacoInstance,
      editor
    } = this, { Selection } = monacoInstance, selections = [], imports = model.findMatches("^import .*$", true, true);
    let blockStart, blockEnd = true;
    [
      "baselines",
      "children",
      "segments",
      "intervals",
      "events",
      "tasks",
      "resources",
      "assignments",
      "dependencies",
      "timeRanges",
      "resourceTimeRanges",
      "rows"
    ].forEach((blockName) => {
      const blockStartMatch = `${blockName}\\s*(?:=|:)\\s*\\[`;
      for (blockStart = model.findForwards(blockStartMatch, fileStart, true); blockStart && blockEnd; blockStart = model.findForwards(blockStartMatch, blockEnd.range.getEndPosition(), true)) {
        blockEnd = model.findForwards("\\](?:;|,)?\\s*$", blockStart.range.getEndPosition(), true);
        if (blockEnd) {
          while (blockEnd && selections.some((s) => s.containsRange(blockEnd.range))) {
            blockEnd = model.findForwards("\\](?:;|,)?\\s*$", blockEnd.range.getEndPosition(), true);
          }
          if (blockEnd) {
            selections.push(new Selection(blockStart.range.startLineNumber, 1, blockEnd.range.endLineNumber + 1, 1));
          }
        }
      }
    });
    let hideStart, lastRangeEnd = fileStart;
    while (hideStart = model.findForwards("^\\s*//\\s*hide", lastRangeEnd, true)) {
      const hideEnd = model.findForwards("^\\s*//\\s*end(?:-|(?:\\s*))?hide", {
        lineNumber: hideStart.range.endLineNumber,
        column: 0
      }, true);
      if (hideEnd) {
        selections.push(new Selection(hideStart.range.startLineNumber, 1, hideEnd.range.endLineNumber + 1, 1));
      }
      lastRangeEnd = (hideEnd == null ? void 0 : hideEnd.range.getEndPosition()) || fileEnd;
    }
    lastRangeEnd = fileStart;
    while (hideStart = model.findForwards("^\\s*//\\s*region", lastRangeEnd, true)) {
      const hideEnd = model.findForwards("^\\s*//\\s*end(?:-|(?:\\s*))?region", {
        lineNumber: hideStart.range.endLineNumber,
        column: 0
      }, true);
      if (hideEnd) {
        selections.push(new Selection(hideStart.range.startLineNumber, 1, hideEnd.range.endLineNumber + 1, 1));
      }
      lastRangeEnd = (hideEnd == null ? void 0 : hideEnd.range.getEndPosition()) || fileEnd;
    }
    if (imports.length) {
      selections.push(new Selection(1, 1, imports[imports.length - 1].range.startLineNumber + 1, 1));
    }
    for (const selection of selections) {
      editor.setSelections([...editor.getSelections(), selection]);
      await editor.getAction("editor.createFoldingRangeFromSelection").run();
    }
  }
  set status(status) {
    this.widgetMap.status.html = status;
  }
  get isReadOnly() {
    const me = this;
    return me.isFramework || ["html", "scss", "json"].includes(me.fileExt) || me.fileExt === "js" && (me.hasImports || isUmd);
  }
  toggleReadOnly() {
    const me = this, { contentElement, isReadOnly } = me;
    contentElement.classList.toggle("readonly", isReadOnly);
    if (isReadOnly) {
      me.status = "Read only" + (BrowserHelper.isCSP ? " (Restricted by Content Security Policy)" : !BrowserHelper.isChrome && !BrowserHelper.isFirefox ? " (try it on Chrome or Firefox)" : "");
    } else {
      me.status = "Idle";
    }
    me.editor.updateOptions({ readOnly: isReadOnly });
  }
  async loadCode(filename) {
    filename = `${this.appFolder}${filename}`;
    const me = this, { isVanilla } = me;
    let code = me.codeCache[filename], exception = null;
    me.filename = filename;
    if (!code) {
      try {
        const hash = Math.floor((/* @__PURE__ */ new Date()).getTime() / 36e5), response = await AjaxHelper.get(`${location.href.replace(/[^/]*$/, "")}${filename}?${hash}`);
        code = me.codeCache[filename] = await response.text();
      } catch (e) {
        code = "";
        exception = e;
      }
    }
    me.loadedCode = code;
    me.fileExt = filename.split(".").pop();
    let language = "plaintext";
    switch (me.fileExt) {
      case "js":
      case "mjs":
      case "jsx":
        language = "javascript";
        break;
      case "ts":
      case "mts":
      case "tsx":
      case "vue":
        language = "typescript";
        break;
      case "css":
        language = "css";
        break;
      case "scss":
        language = "scss";
        break;
      case "htm":
      case "html":
        language = "html";
        break;
      case "json":
        language = "json";
        break;
    }
    me.language = language;
    const model = me.model = await me.loadText(code, language);
    model.findForwards = function(searchFor, searchStart) {
      const result = model.findNextMatch(...arguments);
      if (result && !result.range.getStartPosition().isBefore(searchStart)) {
        return result;
      }
    };
    if (isVanilla && me.fileExt === "js") {
      await me.collapseBoilerplate(model);
    }
    me.status = `${exception ? exception.message : "Idle"}`;
    me.toggleReadOnly();
  }
  sortFileNamesWithHierarchy(fileNames) {
    const createHierarchy = (fileNames2) => {
      const hierarchy = {};
      fileNames2.forEach((fileName) => {
        const parts = fileName.split("/");
        let current = hierarchy;
        parts.forEach((part, index) => {
          if (!current[part]) {
            current[part] = index === parts.length - 1 ? null : {};
          }
          current = current[part];
        });
      });
      return hierarchy;
    };
    const sortHierarchy = (hierarchy) => {
      const sortedHierarchy = {};
      const sortedKeys = Object.keys(hierarchy).sort((a, b) => {
        const isAFolder = hierarchy[a] !== null, isBFolder = hierarchy[b] !== null;
        if (isAFolder && !isBFolder)
          return -1;
        if (!isAFolder && isBFolder)
          return 1;
        return a.localeCompare(b);
      });
      sortedKeys.forEach((key) => {
        sortedHierarchy[key] = hierarchy[key] !== null ? sortHierarchy(hierarchy[key]) : hierarchy[key];
      });
      return sortedHierarchy;
    };
    const flattenHierarchy = (hierarchy, prefix = "") => {
      let result = [];
      Object.keys(hierarchy).forEach((key) => {
        const fullPath = `${prefix}${prefix !== "" ? "/" : ""}${key}`;
        if (hierarchy[key] === null) {
          result.push(fullPath);
        } else {
          result = result.concat(flattenHierarchy(hierarchy[key], fullPath));
        }
      });
      return result;
    };
    return flattenHierarchy(sortHierarchy(createHierarchy(fileNames)));
  }
  async initialLoadCode() {
    var _a5;
    const me = this, { widgetMap, preferredSources } = me, { filesCombo } = widgetMap, filesStore = filesCombo.store, appConfig = `${me.appFolder}app.config.json`, appConfigJson = (await AjaxHelper.get(appConfig, { parseJson: true })).parsedJson, sources = [], isOnline = BrowserHelper.isBryntumOnline("online"), appSource = appConfigJson.source || [];
    if (me.isVanilla) {
      const bundles = (_a5 = appConfigJson.index) == null ? void 0 : _a5.bundles, noModule = bundles && !(bundles == null ? void 0 : bundles.includes("module"));
      sources.push(...appSource.flatMap((source) => {
        if (source === "index.html") {
          return isModule && !isOnline && !noModule ? "index.module.html" : isUmd ? "index.umd.html" : "index.html";
        }
        if (source === "app.js") {
          return (isModule || isOnline) && !noModule ? "app.module.js" : isUmd ? "app.umd.js" : "app.js";
        }
        if (source.endsWith(".scss")) {
          return [
            source,
            source.replace(/\.scss$/, ".css")
          ];
        }
        if (!((isModule || isOnline) && source.startsWith("lib/"))) {
          return source;
        }
      }).filter(Boolean));
      if (!isModule && !isUmd) {
        me.hasImports = sources.some(
          (source) => ["lib/", "locales/"].some((dir) => source.startsWith(dir))
        );
      }
    } else {
      if (appSource.length) {
        sources.push(...appConfigJson.source);
      } else {
        console.warn(`No source files loaded from ${appConfig}`);
      }
    }
    filesCombo.items = me.sortFileNamesWithHierarchy(sources).map((src) => ({
      text: src,
      value: src
    }));
    const preferredSource = (preferredSources || []).map((source) => filesStore.find((record) => source.test(record.value))).find(Boolean) || filesStore.first;
    filesCombo.value = preferredSource.value;
    await me.loadCode(preferredSource.value);
    filesCombo.onChange = "up.onFilesComboChange";
    me.toggleReadOnly();
    filesCombo.readOnly = filesStore.count === 1;
  }
  /**
   * Returns default monaco code path
   * @member {String|undefined}
   * @readobnly
   */
  static get monacoCodePath() {
    const match = /(.*?\/)examples/.exec(document.location.href);
    return match ? `${match[1]}examples/_shared/browser/lib/monaco-editor` : void 0;
  }
  /**
   * Shows / hides code editor
   * @param {Core.widget.DemoCodeEditor|undefined} editor current code editor instance
   * @param {Core.widget.Widget} button Show editor button
   * @param {Object} [editorConfig] New editor configuration
   * @returns {Core.widget.DemoCodeEditor}
   */
  static async toggleCodeEditor(editor, button, editorConfig) {
    const isNew = !editor;
    if (isNew) {
      editor = new _DemoCodeEditor({
        mode: "framework",
        appendTo: document.body,
        codePath: _DemoCodeEditor.monacoCodePath,
        appFolder: "../",
        ...editorConfig
      });
      Widget.disableThrow = true;
      button.icon = "b-icon-spinner";
      await editor.initialLoadCode();
      button.icon = "b-icon-code";
    }
    if (editor.collapsed) {
      await editor.expandPanel();
      editor.focus();
    } else {
      await editor.collapsePanel();
    }
    return editor;
  }
};
//region Config
__publicField(_DemoCodeEditor, "$name", "DemoCodeEditor");
__publicField(_DemoCodeEditor, "type", "democodeeditor");
__publicField(_DemoCodeEditor, "configurable", {
  /**
   * Editor mode
   * @config {'vanilla'|'framework'}
   */
  mode: "vanilla",
  /**
   * App folder location
   * @config {String}
   */
  appFolder: "",
  collapsible: {
    type: PanelCollapserOverlay.type,
    direction: "right",
    autoClose: false
    // don't remove the standard tools or the collapsed header height will be different and the animation
    // will start with a snap to the new header height.
    // recollapseTool : null,
    // tool           : null
  },
  collapsed: true,
  /**
   * Preferred source files.
   * First matched file from the list will be shown on initial load.
   * @config {RegExp[]}
   *
   */
  preferredSources: null,
  monitorResize: false,
  /**
   * An editor configuration object to be passed to https://microsoft.github.io/monaco-editor/docs.html#functions/editor.create.html
   * @config {Object}
   */
  editor: {
    lineNumbers: "off",
    foldingImportsByDefault: true,
    minimap: { enabled: false },
    ariaLabel: "Live code editor"
  },
  tbar: {
    overflow: null,
    items: {
      filesCombo: {
        type: Combo.type,
        flex: "1 1 100%",
        monitorResize: false,
        editable: false,
        fields: [{
          name: "text"
        }],
        listItemTpl: ({ text }) => {
          const fileExt = text.split(".").pop();
          let iconCls = "b-fa-file";
          switch (fileExt) {
            case "js":
            case "mjs":
            case "jsx":
            case "ts":
            case "mts":
            case "tsx":
            case "vue":
              iconCls = "b-fa-file-lines";
              break;
            case "css":
            case "scss":
              iconCls = "b-fa-palette";
              break;
            case "htm":
            case "html":
            case "json":
              iconCls = "b-fa-file-code";
              break;
          }
          const lastSlashIndex = text.lastIndexOf("/") + 1, dirPath = text.substring(0, lastSlashIndex), fileName = text.substring(lastSlashIndex);
          return `<span class="b-editor-file-type b-fw-icon ${iconCls}" ></span> <span class="b-editor-folder">${dirPath}</span>${fileName}`;
        },
        picker: {
          maxHeight: "calc(100vh * 3 / 4)"
        }
      }
    }
  },
  codeCache: {}
});
__publicField(_DemoCodeEditor, "loadMonacoEditor", async (path) => {
  if (!CodeEditor.monacoLoadPromise) {
    const loader = DomHelper.createElement({
      tag: "script",
      parent: document.head,
      src: `${path}monaco-editor/min/vs/loader.js`
    });
    await new Promise((resolve) => loader.addEventListener("load", resolve));
    await (CodeEditor.monacoLoadPromise = new Promise((resolve) => {
      const r = globalThis["require"];
      r.config({ paths: { vs: `${path}monaco-editor/min/vs` } });
      r(["vs/editor/editor.main"], function() {
        resolve(globalThis.monaco);
      });
    }));
  }
});
var DemoCodeEditor = _DemoCodeEditor;
DemoCodeEditor.initClass();
DemoCodeEditor._$name = "DemoCodeEditor";

// ../Core/lib/Core/widget/DemoHeader.js
var _a4, _b, _c;
var _DemoHeader = class _DemoHeader extends Container {
  construct(...args) {
    super.construct(...args);
    this.setupToolbar();
  }
  static get titleLink() {
    const match = /(.*?\/)(examples.*?\/frameworks\/.*?)\/(build|out|dist)/.exec(document.location.href);
    return match ? `${match[1]}examples/#example-${match[2].replace(/\//gm, "-").replace("examples-frameworks", "frameworks")}` : "#";
  }
  async toggleCodeEditor() {
    this.codeEditor = await DemoCodeEditor.toggleCodeEditor(
      this.codeEditor,
      this.widgetMap.tools.widgetMap.codeButton,
      {
        appFolder: this.appFolder,
        preferredSources: [
          /App\.[jt]sx?/,
          /\w+Config\.[jt]sx?/,
          /index\.[jt]sx?/
        ]
      }
    );
  }
  async setupToolbar() {
    var _a5, _b2;
    const { location: location2 } = document, isTest = location2.search.includes("test"), isDemoBrowser = location2.href.match(/examples\/frameworks/);
    if (isDemoBrowser || isTest) {
      const demoProduct = ((_a5 = location2.href.match(/\/(\w+)(-trial)?\/[-\w]*examples\//)) == null ? void 0 : _a5[1].toLowerCase()) || "grid", { widgetMap } = this.widgetMap.tools, {
        downloadButton,
        trialButton
      } = widgetMap;
      Object.values(widgetMap).forEach((widget) => widget.hidden = false);
      trialButton.href = `https://bryntum.com/download/?product=${demoProduct}`;
      const appConfig = (_b2 = await AjaxHelper.get(`${this.appFolder}/app.config.json`, { parseJson: true }).catch(() => {
      })) == null ? void 0 : _b2.parsedJson;
      if ((appConfig == null ? void 0 : appConfig.zip) || isTest) {
        downloadButton.href = `${this.appFolder}${appConfig.zip}`;
      } else {
        downloadButton.hidden = true;
      }
      CSSHelper.insertRule("body { flex-direction : row !important }");
      CSSHelper.insertRule("#container { flex: 1 !important; overflow : hidden !important }");
    }
  }
};
__publicField(_DemoHeader, "$name", "DemoHeader");
__publicField(_DemoHeader, "type", "demoheader");
__publicField(_DemoHeader, "configurable", {
  /**
   * The ID of the owner div element, which will have a header as first child
   * @config {String}
   */
  insertFirst: "container",
  /**
   * The path to the application folder to show code viewer
   * @config {String}
   */
  appFolder: "../",
  cls: "demo-header",
  items: [
    {
      type: "container",
      id: "title-container",
      items: [{
        type: "container",
        tag: "a",
        id: "title",
        elementAttributes: {
          href: _DemoHeader.titleLink
        },
        items: [{
          html: ((_a4 = document.title) != null ? _a4 : "Demo").split("-").pop().trim(),
          tag: "h1",
          tooltip: (_c = (_b = document.querySelector('meta[name="description"]')) == null ? void 0 : _b.content) != null ? _c : ""
        }]
      }]
    },
    {
      type: Toolbar.type,
      id: "tools",
      style: {
        border: "none",
        height: "2.8em"
      },
      defaults: {
        style: {
          margin: 0
        },
        cls: "b-raised b-blue"
      },
      items: [
        "->",
        {
          ref: "trialButton",
          type: "button",
          text: "Download Trial",
          cls: "b-green b-raised",
          href: "#",
          hidden: true
        },
        {
          ref: "codeButton",
          type: "button",
          tooltip: "Click to show the code viewer",
          icon: "b-icon-code",
          onClick: "up.toggleCodeEditor",
          hidden: true
        },
        {
          ref: "downloadButton",
          type: "button",
          tooltip: "Download this demo zip archive",
          icon: "b-icon-download",
          href: "#",
          hidden: true
        },
        {
          ref: "fullscreenButton",
          type: "button",
          tooltip: "Full screen",
          icon: "b-icon-fullscreen",
          onClick() {
            if (Fullscreen.enabled) {
              if (!Fullscreen.isFullscreen) {
                Fullscreen.request(document.body);
              } else {
                Fullscreen.exit();
              }
            }
          }
        }
      ]
    }
  ]
});
var DemoHeader = _DemoHeader;
DemoHeader._$name = "DemoHeader";

// ../Core/lib/Core/widget/DurationField.js
var DurationField = class extends TextField {
  static get defaultConfig() {
    return {
      /**
       * The `value` may be set either in object form specifying two properties, a numerical `magnitude` and a
       * string `unit`:
       *
       * ```javascript
       * new DurationField({
       *    value : {
       *      magnitude : 1,
       *      unit      : 'day'
       *    }
       * });
       * ```
       *
       * Or as a string, that is parsed in accordance with current locale rules. The string is taken to be the
       * numeric magnitude, followed by whitespace, then an abbreviation, or name of the unit:
       *
       * ```javascript
       * new DurationField({
       *    value : '1 day'
       * });
       * ```
       *
       * If a Number is passed, the field's current unit is used and just the magnitude is changed.
       *
       * When the passed value includes a unit, that unit will override the {@link #config-unit} configured on the
       * field.
       *
       * Upon read, the value is always a {@link Core.data.Duration} object containing `magnitude` and `unit`.
       *
       * @prp {Core.data.Duration}
       * @accepts {String|Number|DurationConfig|Core.data.Duration}
       * @category Common
       */
      value: null,
      defaultUnit: "day",
      /**
       * When set to `true` the field will use short names of unit durations
       * (as returned by {@link Core.helper.DateHelper#function-getShortNameOfUnit-static}) when creating the
       * input field's display value.
       * @config {Boolean}
       * @category Common
       */
      useAbbreviation: false,
      /**
       * Set to `true` to allow negative duration
       * @config {Boolean}
       * @category Common
       */
      allowNegative: false,
      /**
       * The number of decimal places to allow. Defaults to no constraint.
       * @config {Number}
       * @default
       * @category Common
       */
      decimalPrecision: null,
      /**
       * Widgets that trigger functionality upon click. Each trigger icon is a {@link Core.widget.Widget} instance
       * which may be hidden, shown and observed and styled just like any other widget.
       * @prp {Object<String,Core.widget.Widget>} triggers
       * @accepts {Object<String,FieldTriggerConfig>}
       * @property {FieldTriggerConfig} triggers.spin Shows up and down arrows to increase or decrease the field
       * value based on the {@link #config-step}
       * @property {FieldTriggerConfig} triggers.clear Clears the field value, only available if this field is
       * {@link #config-clearable}
       */
      triggers: {
        spin: {
          type: "spintrigger"
        }
      },
      nullValue: null
    };
  }
  /**
   * Fired when this field's value changes.
   * @event change
   * @param {Core.data.Duration} value - This field's value
   * @param {Core.data.Duration} oldValue - This field's previous value
   * @param {Boolean} valid - True if this field is in a valid state.
   * @param {Event} [event] - The triggering DOM event if any.
   * @param {Boolean} userAction - Triggered by user taking an action (`true`) or by setting a value (`false`)
   * @param {Core.widget.DurationField} source - This field
   */
  /**
   * User performed default action (typed into this field or hit the triggers).
   * @event action
   * @param {Core.data.Duration} value - This field's value
   * @param {Core.data.Duration} oldValue - This field's previous value
   * @param {Boolean} valid - True if this field is in a valid state.
   * @param {Event} [event] - The triggering DOM event if any.
   * @param {Boolean} userAction - Triggered by user taking an action (`true`) or by setting a value (`false`)
   * @param {Core.widget.DurationField} source - This field
   */
  static get configurable() {
    return {
      /**
       * Get/set the min value (e.g. 1d)
       * @member {String} min
       * @category Common
       */
      /**
       * Minimum duration value (e.g. 1d)
       * @config {String}
       * @category Common
       */
      min: null,
      /**
       * Get/set the max value
       * @member {String} max (e.g. 10d)
       * @category Common
       */
      /**
       * Max duration value (e.g. 10d)
       * @config {String}
       * @category Common
       */
      max: null,
      /**
       * Get/set the allowed units, e.g. "day,hour,year".
       * @member {String} allowedUnits
       * @category Common
       */
      /**
       * Comma-separated list of units to allow in this field, e.g. "day,hour,year". Leave blank to allow all
       * valid units (the default)
       * @config {String}
       * @category Common
       */
      allowedUnits: null,
      /**
       * Step size for spin button clicks. The `step` property may be set in object form specifying two properties,
       * `magnitude`, a Number, and `unit`, a String.
       *
       * If a Number is passed, the step's current unit is used (or `day` if no current step set) and just the
       * magnitude is changed.
       *
       * If a String is passed, it is parsed by {@link Core.helper.DateHelper#function-parseDuration-static}, for
       * example `'1d'`, `'1 d'`, `'1 day'`, or `'1 day'`.
       * @config {DurationConfig|Number|String}
       * @default
       * @category Common
       */
      step: 1,
      /**
       * The duration unit to use with the current magnitude value.
       *
       * Note that setting a {@link #config-value} that includes a unit will override the configured unit.
       *
       * @prp {DurationUnit}
       * @category Common
       */
      unit: null,
      /**
       * The duration magnitude to use with the current unit value. Can be either an integer or a float value.
       * Both "," and "." are valid decimal separators.
       * @prp {Number}
       * @category Common
       */
      magnitude: null
    };
  }
  changeMin(value2) {
    return typeof value2 === "string" ? new Duration(value2) : value2;
  }
  changeMax(value2) {
    return typeof value2 === "string" ? new Duration(value2) : value2;
  }
  changeAllowedUnits(units) {
    if (typeof units === "string") {
      units = units.split(",");
    }
    if (units.length > 0 && !units.includes(this.defaultUnit)) {
      this.defaultUnit = units[0];
    }
    return units;
  }
  updateAllowedUnits(units) {
    this.allowedUnitsRe = new RegExp(`^(${units.join("|")})$`, "i");
  }
  get inputValue() {
    return this.value == null ? "" : this.calcValue(true).toString(this.useAbbreviation, this.decimalPrecision);
  }
  updateUnit() {
    this.clearError("L{invalidUnit}");
    super.value = this.calcValue();
  }
  updateMagnitude() {
    this.clearError("L{invalidUnit}");
    super.value = this.calcValue();
  }
  get unitWithDefault() {
    return this.unit || this.defaultUnit;
  }
  roundMagnitude(value2) {
    return value2 && this.decimalPrecision != null ? ObjectHelper.round(value2, this.decimalPrecision) : value2;
  }
  get allowDecimals() {
    return this.decimalPrecision !== 0;
  }
  get isValid() {
    const me = this, inputValue = me.input.value, value2 = DateHelper.parseDuration(inputValue, me.allowDecimals, inputValue.match(/[\p{Letter}\p{Mark}]+/gui) ? void 0 : me.unitWithDefault), isEmpty = !inputValue || value2 && value2.magnitude == null;
    return me.skipValidation || super.isValid && (isEmpty && !me.required || !isEmpty && (me.allowNegative || (value2 == null ? void 0 : value2.magnitude) >= 0));
  }
  internalOnChange(event) {
    const me = this, value2 = me.value, oldVal = me._lastValue;
    if (me.hasChanged(oldVal, value2)) {
      me._lastValue = value2;
      me.triggerFieldChange({ value: value2, event, userAction: true, valid: me.isValid });
    }
  }
  onFocusOut(e) {
    var _a5, _b2, _c2;
    this.syncInputFieldValue(true);
    (_c2 = (_b2 = (_a5 = this.triggers) == null ? void 0 : _a5.spin) == null ? void 0 : _b2.clickRepeater) == null ? void 0 : _c2.cancel();
    return super.onFocusOut(e);
  }
  set value(value2) {
    const me = this;
    let newMagnitude, newUnit;
    me.getConfig("allowedUnits");
    me.clearError("L{invalidUnit}");
    if (typeof value2 === "number" || typeof value2 === "string" && value2.length > 0 && !isNaN(value2)) {
      newMagnitude = Number(value2);
      newUnit = me.unitWithDefault;
    } else if (typeof value2 === "string") {
      if (/^\s*$/.test(value2)) {
        newMagnitude = null;
      } else {
        const parsedDuration = DateHelper.parseDuration(value2, me.allowDecimals, me.unitWithDefault);
        if (parsedDuration) {
          if (!me.allowedUnitsRe || me.allowedUnitsRe.test(parsedDuration.unit)) {
            newUnit = parsedDuration.unit;
            newMagnitude = parsedDuration.magnitude;
          } else {
            me.setError("L{invalidUnit}");
          }
        }
      }
    } else {
      if (value2 && "unit" in value2 && "magnitude" in value2) {
        newUnit = value2.unit;
        newMagnitude = value2.magnitude;
      } else {
        newUnit = null;
        newMagnitude = null;
      }
    }
    if (me.magnitude !== newMagnitude || me.unit != newUnit) {
      me._magnitude = newMagnitude;
      if (newUnit) {
        me.unit = newUnit;
      }
      super.value = me.calcValue();
    }
  }
  okMax(value2) {
    if (typeof value2 === "number") {
      value2 = new Duration({
        unit: this.unitWithDefault,
        magnitude: value2
      });
    }
    return this.max == null || value2 <= this.max;
  }
  okMin(value2) {
    if (typeof value2 === "number") {
      value2 = new Duration({
        unit: this.unitWithDefault,
        magnitude: value2
      });
    }
    return this.min == null || value2 >= this.min;
  }
  get validity() {
    const value2 = this.value, validity = {};
    if (value2 != null) {
      validity.rangeUnderflow = !this.okMin(value2);
      validity.rangeOverflow = !this.okMax(value2);
    }
    validity.valid = !validity.rangeUnderflow && !validity.rangeOverflow;
    return validity;
  }
  get value() {
    return super.value;
  }
  calcValue(round2 = false) {
    const me = this, { magnitude } = me;
    if ((!me.unit || magnitude == null) && me.clearable) {
      return null;
    }
    return new Duration(round2 ? me.roundMagnitude(magnitude) : magnitude, me.unitWithDefault);
  }
  hasChanged(oldValue, newValue) {
    return newValue && !oldValue || !newValue && oldValue || // if unit has changed or the values in MS is different
    newValue && oldValue && !(oldValue.unit === newValue.unit && oldValue.isEqual(newValue));
  }
  /**
   * The `milliseconds` property is a read only property which returns the number of milliseconds in this field's
   * value
   * @member {Number} milliseconds
   * @readonly
   */
  get milliseconds() {
    return this.value ? this.value.milliseconds : 0;
  }
  onInternalKeyDown(keyEvent) {
    const me = this, { _isUserAction } = me;
    me._isUserAction = true;
    if (keyEvent.key === "ArrowUp") {
      me.doSpinUp();
    } else if (keyEvent.key === "ArrowDown") {
      me.doSpinDown();
    }
    me._isUserAction = _isUserAction;
  }
  doSpinUp() {
    const me = this;
    let { value: value2 } = me;
    if (me.readOnly) {
      return;
    }
    if (ObjectHelper.isEmpty(value2)) {
      value2 = new Duration({ magnitude: 0, unit: this.defaultUnit });
    }
    let newValue = value2.add(this._step);
    if (!me.okMin(newValue)) {
      newValue = me.min;
    }
    if (me.okMax(newValue)) {
      me.value = newValue;
    }
  }
  doSpinDown() {
    const me = this;
    let { value: value2 } = me;
    if (me.readOnly) {
      return;
    }
    if (ObjectHelper.isEmpty(value2)) {
      value2 = new Duration({ magnitude: 0, unit: this.defaultUnit });
    }
    let newValue = value2.add({ magnitude: -this._step.magnitude, unit: this._step.unit });
    if (!me.okMax(newValue)) {
      newValue = me.max;
    }
    if (me.okMin(newValue) && (me.allowNegative || (me.magnitude || 0) > 0)) {
      me.value = newValue;
    }
  }
  changeStep(value2, was) {
    const type = typeof value2;
    if (!value2) {
      return null;
    }
    if (type === "number") {
      value2 = {
        magnitude: Math.abs(value2),
        unit: void 0
      };
    } else if (type === "string") {
      value2 = DateHelper.parseDuration(value2);
    }
    if (value2 && value2.unit && value2.magnitude) {
      if (value2.magnitude < 0) {
        value2 = {
          magnitude: -value2.magnitude,
          unit: value2.unit
        };
      }
    }
    return value2;
  }
  updateStep(value2) {
    this.element.classList.toggle("b-no-steppers", Boolean(value2));
    this.syncInvalid();
  }
};
__publicField(DurationField, "$name", "DurationField");
__publicField(DurationField, "type", "durationfield");
__publicField(DurationField, "alias", "duration");
DurationField.initClass();
DurationField._$name = "DurationField";

// ../Core/lib/Core/widget/Editor.js
var Editor = class extends Container {
  //endregion
  //region Events
  /**
   * Fired before the editor is shown to start an edit operation. Returning `false` from a handler vetoes the edit operation.
   * @event beforeStart
   * @param {HTMLElement} target The element which the Editor is to overlay to edit its content.
   * @param {Core.data.Model} [record] The record being edited.
   * @param {String} [field] The name of the field if a record is being edited.
   * @param {Object} value - The value to be edited.
   * @param {String|AlignSpec} align - How to align the editor.
   * @param {Boolean} hideTarget `true` if the target is to be hidden during the edit.
   * @param {Boolean|Object} matchSize Whether to match the target size. See {@link #config-matchSize}
   * @param {Boolean} matchSize.width Match width
   * @param {Boolean} matchSize.height Match height
   * @param {Boolean} matchFont Whether to match the target's font. See {@link #config-matchFont}
   * @param {Boolean} focus Whether to focus the editor's field.
   * @preventable
   */
  /**
   * Fired when an edit operation has begun.
   * @event start
   * @param {Object} value - The starting value of the field.
   * @param {Core.widget.Editor} source - The Editor that triggered the event.
   */
  /**
   * Fired when an edit completion has been requested, either by `ENTER`, or focus loss (if configured to complete on blur).
   * The completion may be vetoed, in which case, focus is moved back into the editor.
   * @event beforeComplete
   * @param {Object} oldValue - The original value.
   * @param {Object} value - The new value.
   * @param {Core.widget.Editor} source - The Editor that triggered the event.
   * @param {Function} [finalize] An async function may be injected into this property
   * which performs asynchronous finalization tasks such as complex validation of confirmation. The
   * value `true` or `false` must be returned.
   * @param {Object} [finalize.context] An object describing the editing context upon requested completion of the edit.
   * @preventable
   */
  /**
   * Edit has been completed, and any associated record or element has been updated.
   * @event complete
   * @param {Object} oldValue - The original value.
   * @param {Object} value - The new value.
   * @param {Core.widget.Editor} source - The Editor that triggered the event.
   */
  /**
   * Fired when cancellation has been requested, either by `ESC`, or focus loss (if configured to cancel on blur).
   * The cancellation may be vetoed, in which case, focus is moved back into the editor.
   * @event beforeCancel
   * @param {Object} oldValue - The original value.
   * @param {Object} value - The new value.
   * @param {Event} event - Included if the cancellation was triggered by a DOM event
   * @param {Core.widget.Editor} source - The Editor that triggered the event.
   * @preventable
   */
  /**
   * Edit has been canceled without updating the associated record or element.
   * @event cancel
   * @param {Object} oldValue - The original value.
   * @param {Object} value - The value of the field.
   * @param {Event} event - Included if the cancellation was triggered by a DOM event
   * @param {Core.widget.Editor} source - The Editor that triggered the event.
   */
  /**
   * Fire to relay a `keydown` event from the field.
   * @event keyDown
   * @param {Event} event - The key event.
   */
  //endregion
  afterConfigure() {
    const me = this;
    super.afterConfigure();
    me.onTargetSizeChange = me.onTargetSizeChange.bind(me);
    EventHelper.on({
      element: me.element,
      keydown: "onEditorKeyDown",
      contextmenu: "stopMouseEvents",
      mousedown: "stopMouseEvents",
      mouseover: "stopMouseEvents",
      mouseout: "stopMouseEvents",
      mouseup: "stopMouseEvents",
      click: "stopMouseEvents",
      dblclick: "stopMouseEvents",
      thisObj: me
    });
    me.ion({
      beforeHide: "beforeEditorHide",
      hide: "afterEditorHide"
    });
    LocaleManager_default.ion({
      locale: "onLocaleChange",
      thisObj: me
    });
  }
  onLocaleChange() {
    const { inputField } = this;
    if (inputField && !inputField.isDestroyed) {
      if (inputField.syncInputFieldValue) {
        inputField.syncInputFieldValue(true);
      } else if (!(inputField instanceof Field)) {
      }
    }
  }
  render(renderTo) {
    const oldParent = this.element.parentNode, [parentElement] = this.getRenderContext(this, renderTo);
    parentElement.classList.add("b-editing");
    super.render(...arguments);
    if ((oldParent == null ? void 0 : oldParent.classList) && parentElement !== oldParent) {
      oldParent.classList.remove("b-editing");
    }
  }
  /**
   * Start editing
   * @param {Object} editObject An object containing details about what to edit.
   * @param {HTMLElement|Core.helper.util.Rectangle} editObject.target the element or Rectangle to align to.
   * @param {String} [editObject.align=t0-t0] How to align to the target.
   * @param {Boolean} [editObject.matchSize=true] Match editor size to target size.
   * @param {Boolean} [editObject.matchFont=true] Match editor's font-size size to target's font-size.
   * @param {Core.data.Model} [editObject.record] The record to edit.
   * @param {String} [editObject.field] The field name in the record to edit. This defaults to the `name` of the
   * {@link #config-inputField}. Also if record has method set + capitalized field, method will be called, e.g. if
   * record has method named `setFoobar` and this config is `foobar`, then instead of `record.foobar = value`,
   * `record.setFoobar(value)` will be called.
   * @param {Object} [editObject.value] The value to edit.
   * @param {Boolean} [editObject.focus=true] Focus the field.
   * @param {Boolean} [editObject.fitTargetContent] Pass `true` to allow the Editor to expand beyond the width of its
   * target element if its content overflows horizontally. This is useful if the editor has triggers to display, such
   * as a combo.
   * @returns {Promise} Resolved promise returns`true` if editing has been started, `false` if an
   * {@link #event-beforeStart} listener has vetoed the edit.
   */
  async startEdit(editObject) {
    var _a5, _b2, _c2;
    const me = this;
    editObject = ObjectHelper.assignIf(editObject, {
      align: me.align,
      hideTarget: me.hideTarget,
      matchSize: me.matchSize,
      matchFont: me.matchFont,
      fitTargetContent: me.fitTargetContent,
      focus: true
    });
    if (me.isVisible) {
      await me.completeEdit();
    }
    if (me.trigger("beforeStart", editObject) !== false) {
      const {
        target,
        hideTarget,
        matchSize,
        matchFont,
        fitTargetContent,
        record,
        field = me.inputField.name,
        focus
      } = editObject, { inputField, appendToTargetParent } = me, { input } = inputField, targetFontSize = DomHelper.getStyleValue(target, "font-size"), targetFontFamily = DomHelper.getStyleValue(target, "font-family");
      let { value: value2, align } = editObject;
      if (record && field) {
        me.record = record;
        me.dataField = field;
        if (inputField.assignFieldValue !== Widget.prototype.assignFieldValue) {
          value2 = inputField.value;
        } else if (value2 === void 0) {
          value2 = record.getValue(field);
        }
      }
      if (appendToTargetParent && target.nodeType === Node.ELEMENT_NODE) {
        target.parentElement.appendChild(me.element);
      }
      if (matchSize) {
        if (target instanceof HTMLElement) {
          me.updateSize(target, matchSize);
        }
        if (target instanceof HTMLElement) {
          ResizeMonitor.addResizeListener(target, me.onTargetSizeChange);
        }
      }
      if (input) {
        if (matchFont) {
          input.style.fontSize = targetFontSize;
          input.style.fontFamily = targetFontFamily;
        } else {
          input.style.fontSize = input.style.fontFamily = "";
        }
      }
      me.assigningValues = true;
      (_a5 = inputField.clearError) == null ? void 0 : _a5.call(inputField);
      if ("setValue" in inputField) {
        await inputField.setValue(value2);
      } else {
        inputField.value = value2;
      }
      me.assigningValues = false;
      if (typeof align === "string") {
        align = { align };
      }
      await me.showBy({
        target,
        allowTargetOut: true,
        ...align
      });
      if (fitTargetContent) {
        const overflow = input.scrollWidth - input.clientWidth;
        if (overflow > 0) {
          me.width += overflow + DomHelper.scrollBarWidth;
        }
      }
      focus && ((_b2 = inputField.focus) == null ? void 0 : _b2.call(inputField));
      if (target.nodeType === Element.ELEMENT_NODE) {
        if (hideTarget) {
          target.classList.add("b-hide-visibility");
        }
      }
      me.editing = true;
      const convertedValue = inputField.value;
      me.trigger("start", { value: convertedValue });
      if (Array.isArray(convertedValue) && inputField.editingRecords && ((_c2 = convertedValue[0]) == null ? void 0 : _c2.isModel)) {
        me.oldValue = convertedValue.map((record2) => record2.copy(record2.id));
      } else {
        me.oldValue = convertedValue;
        if (Array.isArray(me.oldValue)) {
          me.oldValue = me.oldValue.slice();
        }
      }
      inputField.initialValue = me.oldValue;
      return true;
    }
    return false;
  }
  async refreshEdit() {
    if (this.isVisible) {
      const { record, dataField, inputField, owner } = this;
      if (record) {
        ((owner == null ? void 0 : owner.readOnly) || record.readOnly) && this.cancelEdit();
        if (dataField) {
          const value2 = record.getValue(dataField);
          if (!ObjectHelper.isEqual(inputField.value, value2)) {
            await inputField.setValue(value2);
          }
        }
      }
    }
  }
  finishEdit() {
    const { target, aligningToElement } = this.lastAlignSpec;
    if (aligningToElement) {
      target.classList.remove("b-editing");
      target.classList.remove("b-hide-visibility");
    }
    this.editing = false;
    this.trigger("afterEdit", { record: this.record });
  }
  onEditorKeyDown(event) {
    var _a5;
    const me = this;
    switch (event.key) {
      case me.completeKey: {
        event.preventDefault();
        event.stopImmediatePropagation();
        me.completeEdit(null, event);
        break;
      }
      case me.cancelKey:
        me.cancelEdit(event);
        event.stopImmediatePropagation();
        break;
    }
    (_a5 = me.trigger) == null ? void 0 : _a5.call(me, "keyDown", { event });
  }
  stopMouseEvents(e) {
    if (!this.allowMouseEvents) {
      e.stopPropagation();
    }
  }
  onFocusOut(event) {
    super.onFocusOut(event);
    const me = this;
    if (!me.isFinishing && me.editing) {
      switch (me.blurAction) {
        case "cancel":
          me.cancelEdit(event);
          break;
        case "complete":
          me.completeEdit(null, event);
      }
    }
  }
  /**
   * Complete the edit, and, if associated with a record, update the record if possible.
   * If editing is completed, the editor is hidden.
   *
   * If the field is invalid, the `{@link #config-invalidAction}` config is used to decide
   * upon the course of action.
   *
   * If a {@link #event-beforeComplete} handler returns `false` then editing is not completed.
   *
   * If the field's values has not been changed, then editing is terminated through {@link #function-cancelEdit}.
   *
   * @returns {Boolean} `true` if editing ceased, `false` if the editor is still active.
   */
  async completeEdit(finalize, triggeredByEvent) {
    var _a5, _b2, _c2, _d;
    const me = this, { inputField, oldValue, record } = me, invalidAction = inputField.invalidAction || (inputField.allowInvalid === false ? "block" : me.invalidAction), { value: value2 } = inputField;
    if (!me.isVisible) {
      return;
    }
    if (!inputField.isValid && invalidAction !== "allow") {
      (triggeredByEvent == null ? void 0 : triggeredByEvent.key) && triggeredByEvent.preventDefault();
      if (invalidAction === "block") {
        (_a5 = inputField.focus) == null ? void 0 : _a5.call(inputField);
        return false;
      } else if (invalidAction === "revert") {
        return me.cancelEdit(triggeredByEvent) !== false;
      }
    } else if (ObjectHelper.isEqual(value2, oldValue)) {
      return me.cancelEdit(triggeredByEvent) !== false;
    } else {
      (triggeredByEvent == null ? void 0 : triggeredByEvent.key) && triggeredByEvent.stopPropagation();
      const context = { inputField, record, value: value2, oldValue };
      if (await me.trigger("beforeComplete", context) === false) {
        (_b2 = inputField.focus) == null ? void 0 : _b2.call(inputField);
      } else {
        if (!finalize) {
          finalize = context.finalize || ((_c2 = context.editorContext) == null ? void 0 : _c2.finalize);
        }
        if (finalize) {
          let result = await finalize(context);
          if (result === true) {
            me.onEditComplete();
          } else {
            if (inputField.setError) {
              const error = result || inputField.invalidValueError, clearError = () => {
                listeners();
                inputField.clearError(error);
              }, listeners = inputField.ion({
                change: clearError,
                input: clearError
              });
              inputField.setError(error);
            }
            if (invalidAction === "block") {
              (_d = inputField.focus) == null ? void 0 : _d.call(inputField);
            } else if (invalidAction === "revert") {
              await inputField.setValue(oldValue);
              result = true;
            }
            result = false;
          }
          return result;
        } else {
          me.onEditComplete();
          return true;
        }
      }
    }
    return false;
  }
  /**
   * Cancel the edit and hide the editor.
   */
  cancelEdit(triggeredByEvent) {
    var _a5;
    const me = this, { inputField, oldValue } = me, { value: value2 } = inputField;
    if (me.editing && !me.isFinishing && me.trigger("beforeCancel", { value: value2, oldValue, event: triggeredByEvent }) !== false) {
      (_a5 = inputField.clearError) == null ? void 0 : _a5.call(inputField);
      me.isFinishing = true;
      me.hide();
      if ((triggeredByEvent == null ? void 0 : triggeredByEvent.key) === "Escape") {
        triggeredByEvent.stopImmediatePropagation();
      }
      me.trigger("cancel", { value: value2, oldValue, event: triggeredByEvent });
      me.finishEdit();
      me.isFinishing = false;
    } else {
      triggeredByEvent == null ? void 0 : triggeredByEvent.preventDefault();
      return false;
    }
  }
  // Handle updating what needs to be updated.
  onEditComplete() {
    const me = this, { record, dataField, inputField, oldValue } = me;
    let { value: value2 } = inputField;
    if (!me.isFinishing) {
      me.isFinishing = true;
      me.hide();
      if (record && (!me.instantUpdate || inputField._outstandingUpdate)) {
        const setterName = `set${StringHelper.capitalize(dataField)}`;
        if (record.isFieldModified(dataField) && record.getUnmodified(dataField) == null && (value2 == null || value2 === "")) {
          value2 = record.meta.modified[dataField];
        }
        if (record[setterName]) {
          record[setterName](value2);
        } else {
          record.setValue(dataField, value2);
        }
      }
      me.trigger("complete", { value: value2, oldValue });
      me.finishEdit();
      me.isFinishing = false;
    }
  }
  doDestroy() {
    if (this.createdInputField) {
      this.inputField.destroy();
    }
    super.doDestroy();
  }
  set owner(owner) {
    this._owner = owner;
  }
  // This is a positioned widget appended to a Widget's contentElement. It may have no owner link.
  // Grab the owner by finding what widget it is inside.
  get owner() {
    return this._owner || Widget.fromElement(this.element.parentNode);
  }
  changeInputField(inputField, oldInputField) {
    const me = this;
    if (oldInputField) {
      oldInputField.destroy();
    }
    if (typeof inputField === "string") {
      inputField = {
        type: inputField
      };
    }
    if (!(inputField instanceof Widget)) {
      if (!inputField.type) {
        inputField.type = "textfield";
      }
      inputField = Widget.create(inputField);
      me.createdInputField = true;
    }
    if (me.completeOnChange) {
      inputField.ion({
        change: "onInputFieldChange",
        thisObj: me
      });
    }
    inputField.parent = me;
    return inputField;
  }
  updateInputField(inputField) {
    this.removeAll();
    this.add(inputField);
  }
  get inputField() {
    return this.items[0];
  }
  onInputFieldChange() {
    if (this.containsFocus) {
      this.completeEdit();
    }
  }
  onTargetSizeChange(resizedElement, oldRect, newRect) {
    if (oldRect && newRect) {
      this.updateSize(resizedElement);
    }
  }
  updateSize(targetEl, matchSize = this.lastMatchSize) {
    const me = this, rect = Rectangle.inner(targetEl);
    me.width = matchSize.width === false ? "auto" : rect.width - me.align.offset[0];
    me.height = matchSize.height === false ? "auto" : rect.height;
    me.lastMatchSize = matchSize;
  }
  beforeEditorHide() {
    if (this.editing && !this.isFinishing) {
      this.cancelEdit();
      return false;
    }
  }
  afterEditorHide() {
    var _a5;
    if (this.positioned) {
      this.element.remove();
    }
    ResizeMonitor.removeResizeListener((_a5 = this.lastAlignSpec) == null ? void 0 : _a5.target, this.onTargetSizeChange);
  }
};
//region Config
__publicField(Editor, "$name", "Editor");
__publicField(Editor, "type", "editor");
__publicField(Editor, "configurable", {
  positioned: true,
  hidden: true,
  layout: "fit",
  /**
   * The alignment config for how this editor aligns to a target when asked to {@link #function-startEdit}
   * @config {AlignSpec}
   * @default
   */
  align: {
    align: "t0-t0",
    offset: [0, 0]
  },
  /**
   * Controls whether to hide the target element when asked to {@link #function-startEdit}
   * @config {Boolean}
   * @default
   */
  hideTarget: false,
  /**
   * Set to `true` to automatically append the Editor's element to the parent element of the element being edited.
   * @config {Boolean}
   * @default
   */
  appendToTargetParent: false,
  /**
   * By default, an Editor matches both dimensions, width and height of the element it is targeted at in the
   * {@link #function-startEdit} function.
   *
   * Configure this as false to allow the editor's configured dimensions, or its CSS-imposed dimensions size it.
   *
   * This may also operate with more granularity by specifying both dimensions in an object:
   *
   * ```javascript
   *     // Editor can exceed its target's height
   *     matchSize : {
   *         width  : true,
   *         height : false
   *     }
   * ```
   * @config {Boolean|Object}
   * @property {Boolean} width `true` to match width
   * @property {Boolean} height `true`to match height
   * @default
   */
  matchSize: true,
  /**
   * Controls whether the editor should match target element's font when asked to {@link #function-startEdit}
   * @config {Boolean}
   * @default
   */
  matchFont: true,
  /**
   * Controls whether the editor should expand its width if the input field has overflow {@link #function-startEdit}
   * @config {Boolean}
   * @default
   */
  fitTargetContent: false,
  /**
   * A config object, or the `type` string of the widget (usually a {@link Core.widget.Field} subclass,
   * i.e. {@link Core.widget.TextField}) which this editor will encapsulate.
   * @prp {Core.widget.Widget}
   * @accepts {InputFieldConfig|String}
   * @default
   */
  inputField: "textfield",
  /**
   * What action should be taken when focus moves out of the editor, either by `TAB` or clicking outside.
   * May be `'complete'` or `'cancel`'. Any other value results in no action being taken upon focus leaving the editor
   * leaving the application to listen for the {@link #event-focusOut} event.
   * @config {'complete'|'cancel'|null}
   * @default
   */
  blurAction: "complete",
  /**
   * The name of the `key` which completes the edit.
   *
   * See https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key/Key_Values for key names.
   * @config {String}
   * @default
   */
  completeKey: "Enter",
  /**
   * The name of the `key` which cancels the edit.
   *
   * See https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key/Key_Values for key names.
   * @config {String}
   * @default
   */
  cancelKey: "Escape",
  /**
   * How to handle a request to complete the edit if the field is invalid. There are three choices:
   *  - `block` The default. The edit is not exited, the field remains focused.
   *  - `allow` Allow the edit to be completed.
   *  - `revert` The field value is reverted and the edit is completed.
   * @config {'block'|'allow'|'revert'}
   * @default
   */
  invalidAction: "block",
  /**
   * Configure as `true` to have editing complete as soon as the field fires its `change` event.
   * @config {Boolean}
   * @default false
   */
  completeOnChange: null,
  isolateFields: true,
  instantUpdate: false,
  /**
   * Set to `true` to render the editor field outside the cell element in the browser viewport space, aligned to
   * bottom of the cell. This will also default {@link #config-scrollAction} to `realign` and
   * {@link #config-matchSize} to `false`. It is also recommended to set a `minHeight` and/or `minWidth` property
   * on the {@link #config-align} config.
   * @config {Boolean}
   * @default
   */
  floating: false
});
Editor.initClass();
Editor._$name = "Editor";

// ../Core/lib/Core/helper/NumberHelper.js
var NumberHelper = class {
  static getLocalizedFormatter(formatConfig) {
    if ((formatConfig == null ? void 0 : formatConfig.style) === "currency" && !formatConfig.currency) {
      formatConfig.currency = LocaleManager_default.locale.NumberFormat.currency;
    }
    return typeof formatConfig === "string" ? NumberFormat.get({ locale: LocaleManager_default.localeCode, template: formatConfig }) : (formatConfig == null ? void 0 : formatConfig.locale) != null ? NumberFormat.get(formatConfig) : NumberFormat.get(Object.assign({}, formatConfig, { locale: LocaleManager_default.localeCode }));
  }
};
__publicField(NumberHelper, "$name", "NumberHelper");
NumberHelper._$name = "NumberHelper";

// ../Core/lib/Core/widget/NumberField.js
var preventDefault = (e) => e.ctrlKey && e.preventDefault();
var NumberField = class extends Field {
  static get configurable() {
    return {
      /**
       * Reset to min value when max value is reached using steppers, and vice-versa.
       * @config {Boolean}
       * @default false
       */
      wrapAround: null,
      /**
       * Min value
       * @config {Number}
       */
      min: null,
      /**
       * Max value
       * @config {Number}
       */
      max: null,
      /**
       * Step size for spin button clicks.
       * @member {Number} step
       */
      /**
       * Step size for spin button clicks. Also used when pressing up/down keys in the field.
       * Note that when percent formatting {@link #config-format} is configured, the step size is divided by 100
       * because the internal value is a fractional number.
       * Incrementing/decrementing by `step : 1` changes the value by 0.01, which equals 1%.
       * @config {Number}
       * @default
       */
      step: 1,
      /**
       * Large step size, defaults to 10 * `step`. Applied when pressing SHIFT and stepping either by click or
       * using keyboard.
       * Note that when percent formatting {@link #config-format} is configured, the step size is divided by 100
       * because the internal value is a fractional number.
       * Incrementing/decrementing by `largeStep : 10` changes the value by 0.1, which equals 10%.
       * @config {Number}
       * @default 10
       */
      largeStep: 0,
      /**
       * Initial value
       * @config {Number}
       */
      value: null,
      /**
       * The format to use for rendering numbers.
       *
       * For example:
       * ```
       *  format: '9,999.00##'
       * ```
       * The above enables digit grouping and will display at least 2 (but no more than 4) fractional digits.
       * @config {String|NumberFormatConfig}
       * @default
       */
      format: "",
      /**
       * Widgets that trigger functionality upon click. Each trigger icon is a {@link Core.widget.Widget} instance
       * which may be hidden, shown and observed and styled just like any other widget.
       * @prp {Object<String,Core.widget.Widget>} triggers
       * @accepts {Object<String,FieldTriggerConfig>}
       * @property {FieldTriggerConfig} triggers.spin Shows up and down arrows to increase or decrease the field
       * value based on the {@link #config-step}
       * @property {FieldTriggerConfig} triggers.clear Clears the field value, only present if this field is
       * {@link #config-clearable}
       */
      triggers: {
        spin: {
          type: "spintrigger"
        }
      },
      /**
       * Controls how change events are triggered when stepping the value up or down using either spinners or
       * arrow keys.
       *
       * Configure with:
       * * `true` to trigger a change event per step
       * * `false` to not trigger change while stepping. Will trigger on blur/Enter
       * * A number of milliseconds to buffer the change event, triggering when no steps are performed during that
       *   period of time.
       *
       * @config {Boolean|Number}
       * @default
       */
      changeOnSpin: true,
      // NOTE: using type="number" has several trade-offs:
      //
      // Negatives:
      //   - No access to caretPos/textSelection. This causes anomalies when replacing
      //     the input value with a formatted version of that value (the caret moves to
      //     the end of the input el on each character typed).
      //   - The above also prevents Siesta/synthetic events from mimicking typing.
      //   - Thousand separators cannot be displayed (input.value = '1,000' throws an
      //     exception).
      // Positives:
      //   - On mobile, the virtual keyboard only shows digits et al.
      //   - validity property on DOM node that handles min/max checks.
      //
      // The above may not be exhaustive, but there is not a compelling reason to
      // use type="number" except on mobile.
      /**
       * This can be set to `'number'` to enable the numeric virtual keyboard on
       * mobile devices. Doing so limits this component's ability to handle keystrokes
       * and format properly as the user types, so this is not recommended for
       * desktop applications. This will also limit similar features of automated
       * testing tools that mimic user input.
       * @config {String}
       * @default text
       */
      inputType: null
    };
  }
  //endregion
  //region Init
  construct(config) {
    super.construct(config);
    const me = this;
    me.input.addEventListener("dblclick", () => {
      me.select();
    });
    if (typeof me.changeOnSpin === "number") {
      me.bufferedSpinChange = me.buffer(me.triggerChange, me.changeOnSpin);
    }
  }
  //endregion
  //region Internal functions
  acceptValue(value2, rawValue) {
    let accept = !isNaN(value2);
    if (accept && !this.hasTextSelection) {
      accept = false;
      const raw = this.input.value, current = parseFloat(raw);
      if (raw !== rawValue) {
        accept = !this.acceptValue(current, raw);
      }
    }
    return accept;
  }
  okMax(value2) {
    return isNaN(this.max) || value2 <= this.max;
  }
  okMin(value2) {
    return isNaN(this.min) || value2 >= this.min;
  }
  internalOnKeyEvent(e) {
    if (e.type === "keydown") {
      const me = this, key = e.key;
      let block;
      if (key === "ArrowUp") {
        me.doSpinUp(e.shiftKey);
        block = true;
      } else if (key === "ArrowDown") {
        me.doSpinDown(e.shiftKey);
        block = true;
      } else if (!e.altKey && !e.ctrlKey && key && key.length === 1) {
        const after = me.getAfterValue(key), afterValue = me.formatter.parseStrict(after), accepted = afterValue === me.value || after === "-" && (isNaN(me.min) || me.min < 0);
        block = !accepted && !me.acceptValue(afterValue, after);
      }
      if (key === "Enter" && me._changedBySilentSpin) {
        me.triggerChange(e, true);
        me._changedBySilentSpin = false;
      }
      if (block) {
        e.preventDefault();
      }
    }
    super.internalOnKeyEvent(e);
  }
  doSpinUp(largeStep = false) {
    const me = this, divisor = me.formatter.is.percent ? 100 : 1, step = (largeStep ? me.largeStep : me.step) / divisor;
    if (me.readOnly) {
      return;
    }
    let newValue = (me.value || 0) + step;
    if (!me.okMin(newValue)) {
      newValue = me.min;
    }
    if (me.okMax(newValue)) {
      me.applySpinChange(newValue);
    } else if (me.wrapAround) {
      newValue = me.min;
      me.applySpinChange(newValue);
    }
  }
  doSpinDown(largeStep = false) {
    const me = this, divisor = me.formatter.is.percent ? 100 : 1, step = (largeStep ? me.largeStep : me.step) / divisor;
    if (me.readOnly) {
      return;
    }
    let newValue = (me.value || 0) - step;
    if (!me.okMax(newValue)) {
      newValue = me.max;
    }
    if (me.okMin(newValue)) {
      me.applySpinChange(newValue);
    } else if (me.wrapAround) {
      newValue = me.max - me.max % step;
      me.applySpinChange(newValue);
    }
  }
  applySpinChange(newValue) {
    const me = this;
    me._isUserAction = true;
    if (me.changeOnSpin !== true) {
      me._changedBySilentSpin = true;
      me.silenceChange = true;
      me.bufferedSpinChange && me.bufferedSpinChange(null, true);
    }
    me.value = newValue;
    me._isUserAction = false;
    me.silenceChange = false;
  }
  triggerChange() {
    if (!this.silenceChange) {
      super.triggerChange(...arguments);
    }
  }
  onFocusOut(e) {
    var _a5, _b2, _c2;
    super.onFocusOut(...arguments);
    const me = this, { input } = me, raw = input.value, value2 = me.formatter.truncate(raw), formatted = isNaN(value2) ? raw : me.formatValue(value2);
    (_c2 = (_b2 = (_a5 = me.triggers) == null ? void 0 : _a5.spin) == null ? void 0 : _b2.clickRepeater) == null ? void 0 : _c2.cancel();
    me.lastTouchmove = null;
    if (raw !== formatted) {
      input.value = formatted;
    }
    if (me._changedBySilentSpin) {
      me.triggerChange(e, true);
      me._changedBySilentSpin = false;
    }
  }
  internalOnInput({ data }) {
    const me = this, { formatter, input } = me, { parser, maximumFractionDigits } = formatter, raw = input.value;
    if ((data == null ? void 0 : data.length) > 1 && !(isFinite(parser.parse(raw)) || raw === "-")) {
      const v = me._lastValue || me.valueOnFocus;
      me.queueMicrotask(() => {
        me._value = NaN;
        me.value = v;
      });
    } else {
      const decimals = parser.decimalPlaces(raw);
      if (formatter.truncator && decimals > maximumFractionDigits) {
        let value2 = raw, valueDecimals;
        const trunc = formatter.truncate(raw);
        if (!isNaN(trunc)) {
          value2 = me.formatValue(trunc);
          valueDecimals = parser.decimalPlaces(value2);
          if (valueDecimals < maximumFractionDigits) {
            value2 += "0".repeat(maximumFractionDigits - valueDecimals);
            valueDecimals = maximumFractionDigits;
          }
          if (valueDecimals < decimals) {
            const pos = raw.length - me.caretPos;
            input.value = value2;
            me.caretPos = value2.length - pos + 1;
          }
        }
      }
      super.internalOnInput(...arguments);
    }
  }
  formatValue(value2) {
    return this.formatter.format(value2);
  }
  get formatter() {
    var _a5;
    return (_a5 = this._formatter) != null ? _a5 : this._formatter = NumberHelper.getLocalizedFormatter(this.format);
  }
  //endregion
  //region Getters/Setters
  updateStep(step) {
    var _a5;
    const me = this;
    me.element.classList.toggle("b-hide-spinner", !step);
    me._step = step;
    if (step && BrowserHelper.isMobile) {
      if (!me.touchMoveListener) {
        me.touchMoveListener = EventHelper.on({
          element: me.input,
          touchmove: "onInputSwipe",
          thisObj: me,
          throttled: {
            buffer: 150,
            alt: preventDefault
          }
        });
      }
    } else {
      (_a5 = me.touchMoveListener) == null ? void 0 : _a5.call(me);
    }
  }
  onInputSwipe(e) {
    const { lastTouchmove } = this;
    if (lastTouchmove) {
      const deltaX = e.screenX - lastTouchmove.screenX, deltaY = lastTouchmove.screenY - e.screenY, delta = Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY;
      this[`doSpin${delta > 0 ? "Up" : "Down"}`]();
    }
    e.preventDefault();
    this.lastTouchmove = e;
  }
  changeLargeStep(largeStep) {
    return largeStep || this.step * 10;
  }
  get validity() {
    const value2 = this.value, validity = {};
    if (value2 != null) {
      validity.rangeUnderflow = !this.okMin(value2);
      validity.rangeOverflow = !this.okMax(value2);
    }
    validity.valid = !validity.rangeUnderflow && !validity.rangeOverflow;
    return validity;
  }
  /**
   * Get/set the NumberField's value, or `undefined` if the input field is empty
   * @property {Number}
   */
  changeValue(value2, was) {
    const me = this;
    if (value2 || value2 === 0) {
      let valueIsNaN;
      if (typeof value2 !== "number") {
        value2 = typeof value2 === "string" ? me.formatter.parse(value2) : Number(value2);
        valueIsNaN = isNaN(value2);
        if (valueIsNaN) {
          value2 = "";
        }
      }
      if (!valueIsNaN && me.format) {
        value2 = me.formatter.round(value2);
      }
    } else {
      value2 = void 0;
    }
    return super.changeValue(value2, was);
  }
  get inputValue() {
    return this.value != null ? this.formatValue(this.value) : "";
  }
  //endregion
  updateLocalization() {
    super.updateLocalization();
    this._formatter = void 0;
    this.syncInputFieldValue();
  }
};
//region Config
__publicField(NumberField, "$name", "NumberField");
__publicField(NumberField, "type", "numberfield");
__publicField(NumberField, "alias", "number");
NumberField.initClass();
NumberField._$name = "NumberField";

// ../Core/lib/Core/widget/FieldFilterPicker.js
var filterableFieldDataTypes = {
  number: true,
  boolean: true,
  string: true,
  date: true,
  duration: true,
  time: true,
  auto: true
};
var isInferrableType = {
  number: true,
  boolean: true,
  string: true
};
var isSupportedDurationField = (field) => {
  var _a5;
  return ((_a5 = field == null ? void 0 : field.column) == null ? void 0 : _a5.type) === "duration";
};
var isFilterableField = (field) => filterableFieldDataTypes[field == null ? void 0 : field.type] || isSupportedDurationField(field);
var emptyString = "";
var clsBase = `b-fieldfilterpicker`;
var multiValueOperators = {
  between: true,
  notBetween: true,
  isIncludedIn: true,
  isNotIncludedIn: true
};
var valueInputTypes = {
  textfield: true,
  datefield: true,
  numberfield: true,
  durationfield: true,
  combo: true,
  timefield: true
};
var commonOperators = {
  empty: { value: "empty", text: "L{isEmpty}", argCount: 0 },
  notEmpty: { value: "notEmpty", text: "L{isNotEmpty}", argCount: 0 },
  "=": { value: "=", text: "L{equals}" },
  "!=": { value: "!=", text: "L{doesNotEqual}" },
  ">": { value: ">", text: "L{isGreaterThan}" },
  "<": { value: "<", text: "L{isLessThan}" },
  ">=": { value: ">=", text: "L{isGreaterThanOrEqualTo}" },
  "<=": { value: "<=", text: "L{isLessThanOrEqualTo}" },
  between: { value: "between", text: "L{isBetween}", argCount: 2 },
  notBetween: { value: "notBetween", text: "L{isNotBetween}", argCount: 2 },
  isIncludedIn: { value: "isIncludedIn", text: "L{isOneOf}", isArrayValued: true },
  isNotIncludedIn: { value: "isNotIncludedIn", text: "L{isNotOneOf}", isArrayValued: true }
};
var extractFieldValue = (field) => {
  const { value: value2 } = field;
  if (field.isTimeField && value2) {
    return DateHelper.getTime(value2);
  }
  return value2;
};
var _FieldFilterPicker = class _FieldFilterPicker extends Container {
  static get defaultValueFieldPlaceholders() {
    return {
      string: "L{enterAValue}",
      number: "L{enterANumber}",
      date: "L{selectADate}",
      relation: "L{selectValue}",
      list: "L{selectOneOrMoreValues}",
      duration: "L{enterAValue}",
      time: "L{selectATime}",
      auto: "L{enterAValue}"
    };
  }
  afterConstruct() {
    const me = this;
    if (!me._fields) {
      throw new Error(`${_FieldFilterPicker.name} requires 'fields' to be configured.`);
    }
    if (!me._filter) {
      throw new Error(`${_FieldFilterPicker.name} requires 'filter' to be configured.`);
    }
    super.afterConstruct();
    const { widgetMap: { propertyPicker, operatorPicker, caseSensitive } } = me;
    propertyPicker.ion({ select: "onPropertySelect", thisObj: me });
    operatorPicker.ion({ select: "onOperatorSelect", thisObj: me });
    caseSensitive.ion({ change: "onCaseSensitiveChange", thisObj: me });
    me.propertyFieldConfig && propertyPicker.setConfig(me.propertyFieldConfig);
    me.operatorFieldConfig && operatorPicker.setConfig(me.operatorFieldConfig);
    propertyPicker.cls = me.allPropertyPickerClasses;
    operatorPicker.cls = me.allOperatorPickerClasses;
    me.populateUIFromFilter();
  }
  changeDateFormat(dateFormat) {
    return this.L(dateFormat);
  }
  get allChildInputs() {
    const { propertyPicker, operatorPicker, caseSensitive } = this.widgetMap;
    return [propertyPicker, operatorPicker, ...this.valueFields, caseSensitive];
  }
  updateDisabled(disabled, was) {
    super.updateDisabled(disabled, was);
    this.allChildInputs.forEach((field) => field.disabled = disabled);
  }
  updateReadOnly(newReadOnly) {
    const { propertyPicker, operatorPicker } = this.widgetMap;
    this.allChildInputs.forEach((field) => field.readOnly = newReadOnly);
    propertyPicker.readOnly = propertyPicker.readOnly || newReadOnly;
    operatorPicker.readOnly = operatorPicker.readOnly || newReadOnly;
  }
  updatePropertyLocked(newPropertyLocked) {
    this.widgetMap.propertyPicker.readOnly = newPropertyLocked || this.readOnly;
    this.widgetMap.propertyPicker.cls = this.allPropertyPickerClasses;
  }
  updateOperatorLocked(newOperatorLocked) {
    this.widgetMap.operatorPicker.readOnly = newOperatorLocked || this.readOnly;
    this.widgetMap.operatorPicker.cls = this.allOperatorPickerClasses;
  }
  changeOperators(newOperators) {
    const operators = newOperators != null ? newOperators : _FieldFilterPicker.defaultOperators;
    return Object.keys(operators).reduce((outOperators, dataType) => ({
      ...outOperators,
      [dataType]: operators[dataType].map((op) => ({ ...op, text: this.L(op.text) }))
    }), {});
  }
  get isMultiSelectValueField() {
    var _a5;
    return ["isIncludedIn", "isNotIncludedIn"].includes((_a5 = this._filter) == null ? void 0 : _a5.operator);
  }
  get allPropertyPickerClasses() {
    var _a5;
    return new DomClassList(`${clsBase}-property`, (_a5 = this.propertyFieldConfig) == null ? void 0 : _a5.cls, {
      [`${clsBase}-combo-locked`]: this.propertyLocked
    });
  }
  get allOperatorPickerClasses() {
    var _a5;
    return new DomClassList(`${clsBase}-operator`, (_a5 = this.operatorFieldConfig) == null ? void 0 : _a5.cls, {
      [`${clsBase}-combo-locked`]: this.operatorLocked
    });
  }
  getValueFieldConfigs() {
    var _a5, _b2, _c2, _d;
    const me = this, {
      valueFieldCls,
      fieldType,
      _filter: { operator },
      onValueChange,
      filterValues,
      isMultiSelectValueField,
      operatorArgCount,
      getValueFieldConfig,
      fieldIsRelation,
      triggerChangeOnInput
    } = me, valueFieldPlaceholders = ObjectHelper.merge(
      {},
      _FieldFilterPicker.defaultValueFieldPlaceholders,
      me.valueFieldPlaceholders
    );
    if (!fieldType || !operator || operatorArgCount === 0) {
      return [];
    }
    let valueFieldCfg = {
      type: "textfield",
      // replaced as needed below
      internalListeners: {
        change: onValueChange,
        input: triggerChangeOnInput ? onValueChange : null,
        thisObj: me
      },
      bubbleEvents: { keydown: true },
      cls: valueFieldCls,
      dataset: {
        type: fieldType
      },
      placeholder: me.L(valueFieldPlaceholders[isMultiSelectValueField ? "list" : fieldIsRelation ? "relation" : fieldType]),
      highlightExternalChange: false
    };
    if (isMultiSelectValueField || fieldIsRelation) {
      valueFieldCfg = {
        ...valueFieldCfg,
        type: "combo",
        multiSelect: isMultiSelectValueField,
        createOnUnmatched: true,
        items: me.getUniqueDataValues(filterValues),
        value: filterValues != null ? filterValues : []
      };
    } else if (operator === "sameTime" || operator === "notSameTime") {
      valueFieldCfg.type = `timefield`;
    } else if (["number", "date", "boolean", "duration", "time"].includes(fieldType)) {
      valueFieldCfg.type = `${fieldType}field`;
    }
    if (fieldType === "date" && valueFieldCfg.type !== "timefield") {
      valueFieldCfg.format = (_b2 = (_a5 = me.selectedField) == null ? void 0 : _a5.format) != null ? _b2 : me.dateFormat;
    } else if (fieldType === "number" && ((_c2 = me.selectedField) == null ? void 0 : _c2.format)) {
      valueFieldCfg.format = (_d = me.selectedField) == null ? void 0 : _d.format;
    }
    if (getValueFieldConfig) {
      valueFieldCfg = me.callback(getValueFieldConfig, me, [me.filter, valueFieldCfg]);
    }
    if (isMultiSelectValueField) {
      return [valueFieldCfg];
    }
    return ArrayHelper.populate(operatorArgCount, (index) => [{
      type: "widget",
      tag: "div",
      cls: `${clsBase}-value-separator`,
      content: me.L("L{FieldFilterPicker.and}")
    }, {
      ...valueFieldCfg,
      value: filterValues[index]
    }]).flat().slice(1);
  }
  /**
   * Return an array of unique values in the data store for the currently selected field. If no store is
   * configured or no field is selected, returns an empty array.
   */
  getUniqueDataValues(extraValuesToInclude = []) {
    var _a5, _b2;
    const me = this, { fieldType } = me, format2 = (_a5 = me.selectedField) == null ? void 0 : _a5.format;
    if (!me.store || !((_b2 = me._filter) == null ? void 0 : _b2.property)) {
      return [];
    }
    const { relatedDisplayField } = me.selectedField;
    let values, sortedValues;
    if (me.fieldIsRelation) {
      const { foreignStore } = me.currentPropertyRelationConfig;
      if (relatedDisplayField) {
        values = foreignStore.allRecords.reduce((options, record) => {
          if (record.id != null) {
            options.push({
              text: record.getValue(relatedDisplayField),
              value: record.id
            });
          }
          return options;
        }, []);
        sortedValues = values.sort((a, b) => me.sortStrings(a.text, b.text));
      } else {
        values = foreignStore.allRecords.map((record) => record.id);
      }
    } else {
      values = me.store.allRecords.map((record) => record.getValue(me._filter.property));
    }
    if (!sortedValues) {
      values.push(...extraValuesToInclude);
      const uniqueValues = ArrayHelper.unique(values.reduce((primitiveValues, value2) => {
        if (value2 != null && String(value2).trim() !== "") {
          if (fieldType === "date") {
            primitiveValues.push(value2.valueOf());
          } else if (fieldType === "duration") {
            primitiveValues.push(value2.toString());
          } else {
            primitiveValues.push(value2);
          }
        }
        return primitiveValues;
      }, []));
      if (fieldType === "string") {
        sortedValues = uniqueValues.sort(me.sortStrings);
      } else if (fieldType === "duration") {
        sortedValues = uniqueValues.map((durationStr) => new Duration(durationStr)).filter((duration) => duration.isValid).sort(me.sortDurations);
      } else {
        sortedValues = uniqueValues.sort(me.sortNumerics);
      }
      if (fieldType === "date") {
        sortedValues = sortedValues.map((timestamp) => {
          const date2 = new Date(timestamp);
          return {
            text: DateHelper.format(date2, format2 != null ? format2 : me.dateFormat),
            value: timestamp
          };
        });
      } else if (fieldType === "number" && format2) {
        const formatter = NumberHelper.getLocalizedFormatter(format2);
        sortedValues = sortedValues.map((number) => ({
          text: formatter.format(number),
          value: number
        }));
      } else if (fieldType === "duration") {
        sortedValues = sortedValues.map((duration) => duration.toString());
      }
    }
    return sortedValues;
  }
  sortStrings(a, b) {
    return (a != null ? a : emptyString).localeCompare(b != null ? b : emptyString);
  }
  sortNumerics(a, b) {
    return a - b;
  }
  sortDurations(a, b) {
    return a.valueOf() - b.valueOf();
  }
  get fieldType() {
    var _a5;
    return (_a5 = this.selectedField) == null ? void 0 : _a5.type;
  }
  get selectedField() {
    var _a5, _b2;
    return (_b2 = this.fields) == null ? void 0 : _b2[(_a5 = this._filter) == null ? void 0 : _a5.property];
  }
  get propertyOptions() {
    var _a5;
    return Object.entries((_a5 = this.fields) != null ? _a5 : {}).filter(
      ([, fieldDef]) => filterableFieldDataTypes[fieldDef.type] || isSupportedDurationField(fieldDef)
    ).map(([fieldName, { title }]) => ({ value: fieldName, text: title != null ? title : fieldName })).sort((a, b) => a.text.localeCompare(b.text));
  }
  get operatorOptions() {
    return this.operators[this.fieldIsRelation ? "relation" : this.fieldType];
  }
  get fieldIsRelation() {
    return Boolean(this.currentPropertyRelationConfig);
  }
  get currentPropertyRelationConfig() {
    var _a5, _b2;
    return (_b2 = (_a5 = this.store) == null ? void 0 : _a5.modelRelations) == null ? void 0 : _b2.find(({ foreignKey }) => {
      var _a6;
      return foreignKey === ((_a6 = this._filter) == null ? void 0 : _a6.property);
    });
  }
  updateOperators() {
    delete this._operatorArgCountLookup;
  }
  /**
   * @internal
   */
  get operatorArgCountLookup() {
    return this._operatorArgCountLookup || (this._operatorArgCountLookup = _FieldFilterPicker.buildOperatorArgCountLookup(this.operators));
  }
  updateFilter() {
    if (this._filter) {
      this.onFilterChange();
    }
  }
  updateStore(newStore) {
    var _a5;
    (_a5 = this._store) == null ? void 0 : _a5.un(this);
    newStore == null ? void 0 : newStore.ion({ refresh: "onStoreRefresh", thisObj: this });
    this.inferFieldTypes();
  }
  onStoreRefresh({ action }) {
    if (this.isMultiSelectValueField && ["dataset", "create", "update", "delete"].includes(action)) {
      this.valueFields[0].items = this.getUniqueDataValues(this.filterValues);
    }
  }
  refreshValueFields() {
    const me = this, { valueFields: valueFieldContainer } = me.widgetMap, {
      valueFields: valueFields2,
      fieldType,
      operatorArgCount,
      _filter: { property, operator }
    } = me, isMultiValue = multiValueOperators[operator], valueFieldType = operator === "sameTime" || operator === "notSameTime" ? "time" : fieldType;
    valueFieldContainer.element.className = new DomClassList({
      [`${clsBase}-values`]: true,
      [`${clsBase}-values-multiple`]: isMultiValue,
      [`${clsBase}-values-${valueFieldType}`]: fieldType !== void 0,
      "b-hidden": property == void 0 || operator == void 0 || operatorArgCount === 0
    });
    const newFieldConfigs = me.getValueFieldConfigs();
    if (newFieldConfigs.length !== valueFields2.length || newFieldConfigs.some(({ type }, fieldIndex) => valueFields2[fieldIndex].type !== type)) {
      valueFieldContainer.removeAll();
      valueFieldContainer.add(newFieldConfigs);
    } else {
      let fieldIndex = 0;
      for (const newFieldConfig of newFieldConfigs) {
        delete newFieldConfig.type;
        ObjectHelper.assign(valueFields2[fieldIndex++], newFieldConfig);
      }
    }
    delete me._valueFields;
    me.refreshCaseSensitive();
  }
  refreshCaseSensitive() {
    var _a5, _b2;
    const me = this, { fieldType, operatorArgCount, isMultiSelectValueField } = me, operator = (_a5 = me._filter) == null ? void 0 : _a5.operator, { caseSensitive } = me.widgetMap;
    caseSensitive.hidden = fieldType !== "string" || !operator || isMultiSelectValueField || operatorArgCount === 0;
    caseSensitive.checked = ((_b2 = me._filter) == null ? void 0 : _b2.caseSensitive) !== false;
  }
  onPropertySelect(event) {
    var _a5;
    const me = this, { _filter } = me;
    _filter.property = ((_a5 = event.record) == null ? void 0 : _a5.data.value) || null;
    if (me.fieldType !== me._fieldType) {
      _filter.operator = null;
      _filter.value = null;
    }
    me._fieldType = _filter.type = me.fieldType;
    me.refreshOperatorPicker();
    me.refreshValueFields();
    me.triggerChange();
  }
  onCaseSensitiveChange({ checked }) {
    this._filter.caseSensitive = checked;
    this.triggerChange();
  }
  onOperatorSelect(event) {
    var _a5;
    const me = this, wasMultiSelectValueField = me.isMultiSelectValueField;
    const prevArgCount = this.operatorArgCount;
    me._filter.operator = ((_a5 = event.record) == null ? void 0 : _a5.data.value) || null;
    if (me.operatorArgCount !== prevArgCount) {
      me._filter.value = null;
    }
    if (me.isMultiSelectValueField && !wasMultiSelectValueField) {
      me._filter.value = [];
    }
    me.refreshValueFields();
    me.triggerChange();
  }
  triggerChange() {
    const { filter: filter2, isValid } = this;
    this.trigger("change", {
      filter: filter2,
      isValid
    });
  }
  onValueChange() {
    const me = this, { isMultiSelectValueField, fieldType, _filter } = me, values = this.valueFields.map(extractFieldValue);
    if (isMultiSelectValueField && fieldType === "date") {
      _filter.value = values[0].map((timestamp) => new Date(timestamp));
    } else if (isMultiSelectValueField && fieldType === "duration") {
      _filter.value = values[0].map((durationStr) => new Duration(durationStr));
    } else {
      if (fieldType === "date" && _filter.operator === "between" && DateHelper.isValidDate(values[1])) {
        values[1].setHours(23, 59, 59, 999);
      }
      _filter.value = values.length === 1 ? values[0] : values;
    }
    me.triggerChange();
  }
  refreshOperatorPicker() {
    const { operatorPicker } = this.widgetMap, { _filter: { operator, property }, operatorOptions } = this;
    operatorPicker.items = operatorOptions;
    operatorPicker.value = operator;
    operatorPicker.hidden = property === null;
  }
  populateUIFromFilter(forceRefreshValueFields = false) {
    const me = this, {
      filterValues,
      widgetMap: { propertyPicker, operatorPicker },
      _filter: { property, operator, disabled },
      propertyOptions,
      operatorOptions,
      isMultiSelectValueField
    } = me;
    propertyPicker.items = propertyOptions;
    operatorPicker.items = operatorOptions;
    operatorPicker.hidden = property === null;
    let refreshValueFields = forceRefreshValueFields;
    if (propertyPicker.value !== property) {
      propertyPicker.value = property;
      me.refreshOperatorPicker();
      refreshValueFields = true;
    }
    if (me.fieldType === "date") {
      if (operator === "=") {
        me._filter.operator = "sameDay";
      } else if (operator === "!=") {
        me._filter.operator = "notSameDay";
      }
    }
    if (operatorPicker.value !== operator) {
      if (operator === null || !operatorPicker.items.find(({ value: value2 }) => value2 === operator)) {
        operatorPicker.clear();
      } else {
        operatorPicker.value = operator;
      }
      refreshValueFields = true;
    }
    if (!operator && operatorPicker.items.length === 1) {
      operatorPicker.value = me._filter.operator = operatorPicker.items[0].value;
    }
    if (refreshValueFields) {
      me.refreshValueFields();
    }
    me.refreshCaseSensitive();
    me.valueFields.forEach((valueField, fieldIndex) => {
      if (isMultiSelectValueField && (valueField.value.length > 0 || filterValues.length > 0)) {
        if (me.fieldType === "date") {
          valueField.value = filterValues.map((date2) => date2 == null ? void 0 : date2.valueOf());
        } else if (me.fieldType === "duration") {
          valueField.value = filterValues.map((duration) => duration == null ? void 0 : duration.toString());
        } else {
          valueField.value = filterValues;
        }
      } else if (fieldIndex >= filterValues.length) {
        valueField.clear();
      } else {
        valueField.value = filterValues[fieldIndex];
      }
    });
    me.allChildInputs.forEach((widget) => widget.disabled = me.disabled || disabled);
  }
  get valueFields() {
    return this._valueFields || (this._valueFields = this.widgetMap.valueFields.queryAll(
      (w) => w.owner === this.widgetMap.valueFields && valueInputTypes[w.type]
    ));
  }
  get filterValues() {
    var _a5;
    if (((_a5 = this._filter) == null ? void 0 : _a5.value) == null) {
      return [];
    }
    return ArrayHelper.asArray(this._filter.value);
  }
  // Must be called manually when filter modified externally
  onFilterChange() {
    const me = this, newFieldType = me.fieldType, forceRefreshValueFields = newFieldType !== me._fieldType;
    me._fieldType = me._filter.type = newFieldType;
    me.populateUIFromFilter(forceRefreshValueFields);
  }
  get operatorArgCount() {
    const { fieldType, filter: { operator }, operatorArgCountLookup } = this;
    return fieldType && operator ? operatorArgCountLookup[fieldType][operator] : 1;
  }
  get isValid() {
    const me = this, { filter: filter2, fieldType, filterValues, isMultiSelectValueField, operatorArgCount } = me, { operator } = filter2, missingValue = operatorArgCount > 0 && (filter2 == null ? void 0 : filter2.value) == null;
    return Boolean(
      // fieldType here validates that we have a matching field
      fieldType && operator && !missingValue && (isMultiSelectValueField && filterValues.length > 0 || filterValues.length === operatorArgCount) && filterValues.every((value2) => value2 != null && (fieldType !== "duration" || value2.isValid))
    );
  }
  get focusElement() {
    var _a5, _b2, _c2, _d;
    return (_d = (_c2 = (_b2 = (_a5 = this.valueFields.find((f) => f.isEmptyInput)) != null ? _a5 : this.operatorPicker) != null ? _b2 : this.propertyPicker) == null ? void 0 : _c2.input) != null ? _d : super.focusElement;
  }
  inferFieldTypes() {
    var _a5;
    if (((_a5 = this.store) == null ? void 0 : _a5.totalCount) > 0 && this.fields) {
      for (const [fieldName, fieldDef] of Object.entries(this.fields)) {
        if (fieldDef.type === "auto") {
          fieldDef.type = _FieldFilterPicker.inferFieldType(this.store, fieldName);
        }
      }
    }
  }
  static inferFieldType(store, fieldName) {
    var _a5;
    const firstValue = (_a5 = store.find((record) => record.getValue(fieldName) != null, true)) == null ? void 0 : _a5.getValue(fieldName);
    if (firstValue !== void 0) {
      const valueType = typeof firstValue;
      if (valueType === "object" && firstValue instanceof Date) {
        return "date";
      } else if (isInferrableType[valueType]) {
        return valueType;
      }
    }
    return "auto";
  }
  static localizeOperator(operator, dataType) {
    const { defaultOperators } = _FieldFilterPicker, typeSearchOrder = Object.keys(defaultOperators).sort(
      (typeA, typeB) => typeA === dataType ? -1 : typeB === dataType ? 1 : 0
    );
    for (const operatorDataType of typeSearchOrder) {
      for (const option of defaultOperators[operatorDataType]) {
        if (option.value === operator) {
          return _FieldFilterPicker.L(option.text);
        }
      }
    }
    return operator;
  }
};
//region Config
__publicField(_FieldFilterPicker, "$name", "FieldFilterPicker");
__publicField(_FieldFilterPicker, "type", "fieldfilterpicker");
__publicField(_FieldFilterPicker, "defaultOperators", {
  string: [
    // In display order
    commonOperators.empty,
    commonOperators.notEmpty,
    commonOperators["="],
    commonOperators["!="],
    { value: "includes", text: "L{contains}" },
    { value: "doesNotInclude", text: "L{doesNotContain}" },
    { value: "startsWith", text: "L{startsWith}" },
    { value: "endsWith", text: "L{endsWith}" },
    commonOperators.isIncludedIn,
    commonOperators.isNotIncludedIn
  ],
  number: [
    commonOperators.empty,
    commonOperators.notEmpty,
    commonOperators["="],
    commonOperators["!="],
    commonOperators[">"],
    commonOperators["<"],
    commonOperators[">="],
    commonOperators["<="],
    commonOperators.between,
    commonOperators.notBetween,
    commonOperators.isIncludedIn,
    commonOperators.isNotIncludedIn
  ],
  date: [
    commonOperators.empty,
    commonOperators.notEmpty,
    { value: "sameDay", text: "L{equals}" },
    { value: "notSameDay", text: "L{doesNotEqual}" },
    { value: "sameTime", text: "L{timeEquals}" },
    { value: "notSameTime", text: "L{timeDoesNotEqual}" },
    { value: "<", text: "L{isBefore}" },
    { value: ">", text: "L{isAfter}" },
    commonOperators.between,
    { value: "isToday", text: "L{isToday}", argCount: 0 },
    { value: "isTomorrow", text: "L{isTomorrow}", argCount: 0 },
    { value: "isYesterday", text: "L{isYesterday}", argCount: 0 },
    { value: "isThisWeek", text: "L{isThisWeek}", argCount: 0 },
    { value: "isNextWeek", text: "L{isNextWeek}", argCount: 0 },
    { value: "isLastWeek", text: "L{isLastWeek}", argCount: 0 },
    { value: "isThisMonth", text: "L{isThisMonth}", argCount: 0 },
    { value: "isNextMonth", text: "L{isNextMonth}", argCount: 0 },
    { value: "isLastMonth", text: "L{isLastMonth}", argCount: 0 },
    { value: "isThisYear", text: "L{isThisYear}", argCount: 0 },
    { value: "isNextYear", text: "L{isNextYear}", argCount: 0 },
    { value: "isLastYear", text: "L{isLastYear}", argCount: 0 },
    { value: "isYearToDate", text: "L{isYearToDate}", argCount: 0 },
    commonOperators.isIncludedIn,
    commonOperators.isNotIncludedIn
  ],
  boolean: [
    { value: "isTrue", text: "L{isTrue}", argCount: 0 },
    { value: "isFalse", text: "L{isFalse}", argCount: 0 }
  ],
  duration: [
    commonOperators.empty,
    commonOperators.notEmpty,
    commonOperators["="],
    commonOperators["!="],
    commonOperators[">"],
    commonOperators["<"],
    commonOperators[">="],
    commonOperators["<="],
    commonOperators.between,
    commonOperators.notBetween,
    commonOperators.isIncludedIn,
    commonOperators.isNotIncludedIn
  ],
  time: [
    commonOperators.empty,
    commonOperators.notEmpty,
    commonOperators["="],
    commonOperators["!="],
    commonOperators[">"],
    commonOperators["<"],
    commonOperators[">="],
    commonOperators["<="],
    commonOperators.between,
    commonOperators.notBetween,
    commonOperators.isIncludedIn,
    commonOperators.isNotIncludedIn
  ],
  relation: [
    commonOperators.empty,
    commonOperators.notEmpty,
    commonOperators["="],
    commonOperators["!="],
    commonOperators.isIncludedIn,
    commonOperators.isNotIncludedIn
  ],
  auto: [
    { value: "*", text: "L{contains}" }
  ]
});
__publicField(_FieldFilterPicker, "configurable", {
  /**
   * Dictionary of {@link #typedef-FieldOption} representing the fields against which filters can be defined,
   * keyed by field name.
   *
   * If filtering a {@link Grid.view.Grid}, consider using {@link Grid.widget.GridFieldFilterPicker}, which can be configured
   * with an existing {@link Grid.view.Grid} instead of, or in combination with, defining fields manually.
   *
   * Example:
   * ```javascript
   * fields: {
   *     // Allow filters to be defined against the 'age' and 'role' fields in our data
   *     age  : { title: 'Age', type: 'number' },
   *     role : { title: 'Role', type: 'string' }
   * }
   * ```
   *
   * @config {Object<String,FieldOption>}
   */
  fields: null,
  /**
   * Make the entire picker disabled.
   *
   * @config {Boolean}
   * @default
   */
  disabled: false,
  /**
   * Make the entire picker read-only.
   *
   * @config {Boolean}
   * @default
   */
  readOnly: false,
  layout: "vbox",
  /**
   * Make only the property selector readOnly.
   *
   * @config {Boolean}
   * @default
   */
  propertyLocked: false,
  /**
   * Make only the operator selector readOnly.
   *
   * @config {Boolean}
   * @default
   */
  operatorLocked: false,
  /**
   * Configuration object for the {@link Core.util.CollectionFilter} displayed
   * and editable in this picker.
   *
   * Example:
   *
   * ```javascript
   * {
   *     property: 'age',
   *     operator: '=',
   *     value: 25
   * }
   * ```
   *
   * @config {CollectionFilterConfig}
   */
  filter: null,
  /**
   * Optional configuration for the property selector {@link Core.widget.Combo}.
   *
   * @config {ComboConfig}
   */
  propertyFieldConfig: null,
  /**
   * Optional configuration for the operator selector {@link Core.widget.Combo}.
   *
   * @config {ComboConfig}
   * @private
   */
  operatorFieldConfig: null,
  /**
   * Optional CSS class to apply to the value field(s).
   *
   * @config {String}
   * @private
   */
  valueFieldCls: null,
  /**
   * Whether to raise {@link #event-change} events as the user types into a value field. If `false`,
   * {@link #event-change} events will be raised only when the value input field's own `change` event
   * occurs, for example on field blur.
   *
   * @config {Boolean}
   * @default
   */
  triggerChangeOnInput: true,
  /**
   * @private
   */
  items: {
    inputs: {
      type: "container",
      layout: "hbox",
      cls: `${clsBase}-inputs`,
      items: {
        propertyPicker: {
          type: "combo",
          items: {},
          cls: `${clsBase}-property`,
          placeholder: "L{FieldFilterPicker.selectAProperty}",
          highlightExternalChange: false
        },
        operatorPicker: {
          type: "combo",
          items: {},
          cls: `${clsBase}-operator`,
          placeholder: "L{FieldFilterPicker.selectAnOperator}",
          highlightExternalChange: false
        },
        valueFields: {
          type: "container",
          cls: `${clsBase}-values`,
          items: {}
        }
      }
    },
    caseSensitive: {
      type: "checkbox",
      text: "L{FieldFilterPicker.caseSensitive}",
      cls: `${clsBase}-case-sensitive`
    }
  },
  /**
   * Overrides the built-in list of operators that are available for selection. Specify operators as
   * an object with data types as keys and lists of operators as values, like this:
   *
   * ```javascript
   * operators : {
   *     string : [
   *         { value : 'empty', text : 'is empty', argCount : 0 },
   *         { value : 'notEmpty', text : 'is not empty', argCount : 0 }
   *     ],
   *     number : [
   *         { value : '=', text : 'equals' },
   *         { value : '!=', text : 'does not equal' }
   *     ],
   *     date : [
   *         { value : '<', text : 'is before' }
   *     ]
   * }
   * ```
   *
   * Here `value` is what will be stored in the `operator` field in the filter when selected, `text` is the text
   * displayed in the Combo for selection, and `argCount` is the number of arguments (comparison values) the
   * operator requires. The default argCount if not specified is 1.
   *
   * **Note:** The operators config is a subset of valid operators described in
   * {@link Core.util.CollectionFilter#typedef-CollectionCompareOperator}.
   * The use of any other operator apart from these is not supported and will lead to unexpected behavior.
   *
   * @config {Object}
   */
  operators: _FieldFilterPicker.defaultOperators,
  /**
   * The date format string used to display dates in value input fields. Defaults to the current locale's
   * `FieldFilterPicker.dateFormat` value.
   *
   * @config {String}
   * @default
   */
  dateFormat: "L{FieldFilterPicker.dateFormat}",
  /**
   * Optional {Core.data.Store} against which filters are being defined. This is used to supply options to filter against
   * when using the 'is one of' and 'is not one of' operators.
   *
   * @config {Core.data.Store}
   */
  store: null,
  /**
   * Optional {@link ValueFieldPlaceholders} object specifying custom placeholder text for value input fields.
   *
   * @config {ValueFieldPlaceholders}
   */
  valueFieldPlaceholders: null,
  /**
   * Optional function that modifies the configuration of value fields shown for a filter. The default configuration
   * is received as an argument and the returned value will be used as the final configuration. For example:
   *
   * ```javascript
   * getValueFieldConfig : (filter, fieldConfig) => {
   *     return {
   *         ...fieldConfig,
   *         title : fieldName    // Override the `title` config for the field
   *     };
   * }
   * ```
   *
   * The supplied function should accept the following arguments:
   *
   * @config {Function}
   * @param {Core.util.CollectionFilter} filter The filter being displayed
   * @param {ContainerItemConfig} fieldConfig Configuration object for the value field
   * @returns {ContainerItemConfig} the resulting configuration
   */
  getValueFieldConfig: null
});
//endregion
// Make lookup of operator arity (arg count) by [fieldType][operator]
__publicField(_FieldFilterPicker, "buildOperatorArgCountLookup", (operators) => ArrayHelper.keyBy(
  Object.entries(operators),
  ([fieldType]) => fieldType,
  ([, operators2]) => ArrayHelper.keyBy(
    operators2,
    ({ value: value2 }) => value2,
    ({ argCount }) => argCount === void 0 ? 1 : argCount
  )
));
var FieldFilterPicker = _FieldFilterPicker;
FieldFilterPicker.initClass();
FieldFilterPicker._$name = "FieldFilterPicker";

// ../Core/lib/Core/widget/Label.js
var Label = class extends Widget {
  compose() {
    const { text, html } = this;
    return {
      tag: "label",
      text,
      html
    };
  }
};
__publicField(Label, "$name", "Label");
__publicField(Label, "type", "label");
__publicField(Label, "configurable", {
  /**
   * Get/set label text
   * @prp {String}
   */
  text: null,
  localizableProperties: ["text"]
});
Label.initClass();
Label._$name = "Label";

// ../Core/lib/Core/widget/FieldFilterPickerGroup.js
var _FieldFilterPickerGroup = class _FieldFilterPickerGroup extends Container {
  afterConstruct() {
    const me = this;
    me.validateConfig();
    const { addFilterButton } = me.widgetMap;
    addFilterButton.ion({ click: "addFilter", thisObj: me });
    addFilterButton.text = me.L(addFilterButton.text);
    me.store && me.updateStore(me.store);
    super.afterConstruct();
  }
  changeDateFormat(dateFormat) {
    return this.L(dateFormat);
  }
  validateConfig() {
    if (!this.fields && !this.store) {
      throw new Error(
        `FieldFilterPickerGroup requires either a 'fields' or 'store' config property.`
      );
    }
  }
  updateFields(newFields) {
    this.widgetMap.pickers.childItems.forEach((picker) => picker.fields = newFields);
  }
  updateFilters(newFilters, oldFilters) {
    const me = this;
    if (oldFilters) {
      oldFilters.filter((filter2) => !newFilters.find((newFilter) => newFilter.id === filter2.id)).forEach((filter2) => {
        var _a5;
        return (_a5 = me.store) == null ? void 0 : _a5.removeFilter(filter2.id);
      });
    }
    newFilters.forEach((filter2) => filter2.id = filter2.id || me.nextFilterId);
    me.widgetMap.pickers.items = (newFilters == null ? void 0 : newFilters.map((filter2) => me.getPickerRowConfig(filter2))) || [];
  }
  changeFilters(newFilters) {
    const { canManageFilter, limitToProperty } = this;
    let filters = newFilters;
    if (canManageFilter) {
      filters = filters.filter((filter2) => this.callback(canManageFilter, this, [filter2]));
    }
    if (limitToProperty) {
      filters = filters.filter((filter2) => filter2.property == null || filter2.property === limitToProperty);
    }
    return filters;
  }
  updateStore(newStore) {
    const me = this;
    me.detachListeners("store");
    if (newStore) {
      let storeFiltersModified = false;
      me.widgetMap.pickers.childItems.forEach(({ widgetMap: { filterPicker: { filter: filter2, isValid } } }) => {
        if (isValid && !newStore.filters.includes(filter2)) {
          newStore.removeFilter(filter2.id, true);
          newStore.addFilter(filter2, true);
          storeFiltersModified = true;
        }
      });
      if (storeFiltersModified) {
        newStore.filter();
      }
      me.appendFiltersFromStore();
      newStore.ion({
        name: "store",
        filter: "onStoreFilter",
        thisObj: me
      });
    }
    me.widgetMap.pickers.childItems.forEach((picker) => picker.store = newStore);
  }
  updateShowAddFilterButton(newShow) {
    this.widgetMap.addFilterButton.hidden = !newShow;
  }
  updateAddFilterButtonText(newText) {
    this.widgetMap.addFilterButton.text = newText != null ? newText : _FieldFilterPickerGroup.addFilterButtonDefaultText;
  }
  /**
   * Find any filters the store has that we don't know about yet, and add to our list
   * @private
   */
  appendFiltersFromStore() {
    const me = this;
    me.store.filters.forEach((filter2) => {
      var _a5;
      const canManage = me.canManage(filter2), { property, operator, id: id2 } = filter2;
      if (canManage && property && operator && !((_a5 = me.filters) == null ? void 0 : _a5.find((filter3) => filter3.id === id2))) {
        me.appendFilter(filter2);
      }
    });
  }
  /**
   * @private
   */
  canManage(filter2) {
    const me = this;
    return (!me.allowedFieldNames || me.allowedFieldNames.includes(filter2.property)) && (!me.canManageFilter || me.callback(me.canManageFilter, me, [filter2]) === true);
  }
  /**
   * Get the configuration object for one child FieldFilterPicker.
   * @param {Core.util.CollectionFilter} filter The filter represented by the child FieldFilterPicker
   * @returns {Object} The FieldFilterPicker configuration
   */
  getFilterPickerConfig(filter2) {
    const me = this, {
      fields,
      store,
      disabled,
      readOnly,
      valueFieldCls,
      operators,
      limitToProperty,
      dateFormat,
      getFieldFilterPickerConfig,
      triggerChangeOnInput,
      allowedFieldNames
    } = me;
    return {
      type: me.constructor.childPickerType,
      fields: fields != null ? fields : me.getFieldsFromStore(store),
      filter: filter2,
      store,
      disabled,
      readOnly,
      propertyLocked: Boolean(limitToProperty),
      valueFieldCls,
      operators,
      dateFormat,
      internalListeners: {
        change: "onFilterPickerChange",
        thisObj: me
      },
      flex: 1,
      triggerChangeOnInput,
      allowedFieldNames,
      ...getFieldFilterPickerConfig ? me.callback(getFieldFilterPickerConfig, me, [filter2]) : void 0
    };
  }
  /**
   * Get store fields as {@link Core.widget.FieldFilterPicker#typedef-FieldOption}s in a dictionary keyed by name.
   * @private
   */
  getFieldsFromStore(store) {
    var _a5, _b2;
    return Object.fromEntries((_b2 = (_a5 = store.modelClass) == null ? void 0 : _a5.fields.map(({ name, type }) => [name, { type }])) != null ? _b2 : []);
  }
  getPickerRowConfig(filter2) {
    const me = this, { disabled, readOnly, canDeleteFilter } = me, canDelete = !(canDeleteFilter && me.callback(canDeleteFilter, me, [filter2]) === false), checked = !Boolean(filter2.disabled);
    return {
      type: "container",
      layout: "box",
      cls: {
        [`b-${_FieldFilterPickerGroup.type}-row`]: true,
        [`b-${_FieldFilterPickerGroup.type}-row-removable`]: canDelete
      },
      dataset: {
        separatorText: me.L("L{FieldFilterPicker.and}")
      },
      items: {
        activeCheckbox: {
          type: "checkbox",
          disabled,
          readOnly,
          checked,
          ariaLabel: me.L("L{FieldFilterPickerGroup.applyCond}", checked),
          internalListeners: {
            change: "onFilterActiveChange",
            thisObj: me
          },
          cls: `b-${_FieldFilterPickerGroup.type}-filter-active`
        },
        filterPicker: me.getFilterPickerConfig(filter2),
        removeButton: {
          type: "button",
          ref: "removeButton",
          disabled,
          readOnly,
          hidden: !canDelete,
          cls: `b-transparent b-${_FieldFilterPickerGroup.type}-remove`,
          icon: "b-fa-trash",
          internalListeners: {
            click: "removeFilter",
            thisObj: me
          }
        }
      }
    };
  }
  get allInputs() {
    const childInputTypes = [this.constructor.childPickerType, "button", "checkbox"];
    return this.queryAll((w) => childInputTypes.includes(w.type));
  }
  updateDisabled(disabled, was) {
    super.updateDisabled(disabled, was);
    this.allInputs.forEach((input) => input.disabled = disabled);
  }
  updateReadOnly(newReadOnly) {
    this.allInputs.forEach((input) => input.readOnly = newReadOnly);
  }
  onFilterActiveChange({ source, checked }) {
    const me = this, filterIndex = me.getFilterIndex(source), filter2 = me.filters[filterIndex], filterPicker = me.getFilterPicker(filterIndex);
    source.input.setAttribute("aria-label", me.L("L{FieldFilterPickerGroup.applyCond}", checked));
    filter2.disabled = !checked;
    filterPicker.onFilterChange();
    if (me.store && filterPicker.isValid) {
      me.store.addFilter(filter2, true);
      me.updateStoreFilter();
    }
    me.triggerChange();
  }
  onFilterPickerChange({ source, filter: filter2, isValid }) {
    const me = this, { store } = me, filterIndex = me.getFilterIndex(source);
    if (store) {
      store.removeFilter(filter2.id, true);
      if (isValid) {
        store.addFilter(filter2, true);
        me.updateStoreFilter();
      }
    }
    Object.assign(me.filters[filterIndex], filter2);
    me.triggerChange();
  }
  getFilterIndex(eventSource) {
    return parseInt(eventSource.parent.element.dataset.itemIndex);
  }
  getPickerRow(index) {
    return this.widgetMap.pickers.childItems[index];
  }
  /**
   * Return the {@link Core.widget.FieldFilterPicker} for the filter at the specified index.
   * @param {Number} filterIndex
   * @returns {Core.widget.FieldFilterPicker}
   */
  getFilterPicker(filterIndex) {
    return this.getPickerRow(filterIndex).widgetMap.filterPicker;
  }
  get nextFilterId() {
    this._nextId = (this._nextId || 0) + 1;
    return `${this.id}-filter-${this._nextId}`;
  }
  removeFilter({ source }) {
    const me = this, filterIndex = me.getFilterIndex(source), filter2 = me.filters[filterIndex], pickerRow = me.getPickerRow(filterIndex), newFocusWidget = me.query((w) => w.isFocusable && w.type !== "container" && !pickerRow.contains(w));
    if (newFocusWidget) {
      newFocusWidget.focus();
    }
    me.removeFilterAt(filterIndex);
    if (me.store) {
      me.store.removeFilter(filter2.id, true);
      me.updateStoreFilter();
    }
    me.trigger("remove", { filter: filter2 });
    me.triggerChange();
  }
  /**
   * Appends a filter at the bottom of the list.
   * @param {CollectionFilterConfig} [filter={}] Configuration object for the {@link Core.util.CollectionFilter} to
   * add. Defaults to an empty filter.
   */
  addFilter({ property = null, operator = null, value: value2 = null } = {}) {
    var _a5;
    const me = this, { filters } = me, newFilter = {
      property: me.limitToProperty || property,
      operator,
      value: value2,
      disabled: false,
      id: me.nextFilterId,
      caseSensitive: false
    };
    if (!me.trigger("beforeAddFilter", { filter: newFilter })) {
      return;
    }
    me.appendFilter(newFilter);
    if (me.getFilterPicker(filters.length - 1).isValid) {
      (_a5 = me.store) == null ? void 0 : _a5.addFilter(newFilter, true);
      me.store && me.updateStoreFilter();
    }
    me.trigger("add", { filter: newFilter });
    me.triggerChange();
  }
  /**
   * @private
   */
  appendFilter(filter2) {
    const me = this;
    if (!me.limitToProperty || filter2.property === me.limitToProperty) {
      me.filters.push(filter2);
      me.widgetMap.pickers.add(
        me.getPickerRowConfig(filter2, me.filters.length - 1)
      );
    }
  }
  onStoreFilter(event) {
    const me = this;
    if (me._isUpdatingStore) {
      return;
    }
    const { filters } = event, storeFiltersById = filters.values.reduce((byId, filter2) => ({ ...byId, [filter2.id]: filter2 }), {});
    for (let filterIndex = me.filters.length - 1; filterIndex >= 0; filterIndex--) {
      const filter2 = me.filters[filterIndex], storeFilter = storeFiltersById[filter2.id], filterRow = me.getPickerRow(filterIndex);
      if (filterRow) {
        const { filterPicker, activeCheckbox } = filterRow.widgetMap;
        if (!storeFilter && filterPicker.isValid) {
          me.removeFilterAt(filterIndex);
        } else if (storeFilter !== void 0) {
          const { operator, value: value2, property, disabled, caseSensitive } = storeFilter;
          if (filter2 !== storeFilter) {
            Object.assign(filter2, { operator, value: value2, property, disabled, caseSensitive });
          }
          filterPicker.filter = filter2;
          filterPicker.onFilterChange();
          activeCheckbox.checked = !disabled;
        }
      }
    }
    me.appendFiltersFromStore();
    me.triggerChange();
  }
  /**
   * Remove the filter at the given index.
   * @param {Number} filterIndex The index of the filter to remove
   */
  removeFilterAt(filterIndex) {
    const { widgetMap: { pickers }, filters } = this;
    pickers.remove(pickers.childItems[filterIndex]);
    filters.splice(filterIndex, 1);
    this.triggerChange();
  }
  /**
   * Trigger a store re-filter after filters have been silently modified.
   * @private
   */
  updateStoreFilter() {
    var _a5;
    this._isUpdatingStore = true;
    (_a5 = this.store) == null ? void 0 : _a5.filter();
    this._isUpdatingStore = false;
  }
  /**
   * Returns the array of filter configuration objects currently represented by this picker group.
   * @type {CollectionFilterConfig[]}
   */
  get value() {
    return this.filters;
  }
  triggerChange() {
    const { filters } = this, validFilters = filters.filter((f, index) => this.getPickerRow(index).widgetMap.filterPicker.isValid);
    this.trigger("change", {
      filters,
      validFilters
    });
  }
  /**
   * Sets all current filters to enabled and checks their checkboxes.
   */
  activateAll() {
    this.setAllActiveStatus(true);
  }
  /**
   * Sets all current filters to disabled and clears their checkboxes.
   */
  deactivateAll() {
    this.setAllActiveStatus(false);
  }
  /**
   * @private
   */
  setAllActiveStatus(newActive) {
    const me = this, { _filters, store } = me;
    _filters.forEach((filter2, filterIndex) => {
      if (newActive === filter2.disabled) {
        const { filterPicker, activeCheckbox } = me.getPickerRow(filterIndex).widgetMap;
        filter2.disabled = !newActive;
        filterPicker.onFilterChange();
        activeCheckbox.checked = newActive;
        if (newActive && store && filterPicker.isValid) {
          store.addFilter(filter2, true);
        }
      }
    });
    me.updateStoreFilter();
  }
  get focusElement() {
    var _a5, _b2;
    return (_b2 = (_a5 = this.getPickerRow(this._filters.length - 1)) == null ? void 0 : _a5.widgetMap.filterPicker.focusElement) != null ? _b2 : super.focusElement;
  }
};
//region Config
__publicField(_FieldFilterPickerGroup, "$name", "FieldFilterPickerGroup");
__publicField(_FieldFilterPickerGroup, "type", "fieldfilterpickergroup");
/**
 * @private
 */
__publicField(_FieldFilterPickerGroup, "addFilterButtonDefaultText", "L{FieldFilterPickerGroup.addFilter}");
__publicField(_FieldFilterPickerGroup, "configurable", {
  /**
   * Array of {@link Core.util.CollectionFilter} configuration objects. One
   * {@link Core.widget.FieldFilterPicker} will be created
   * for each object in the array.
   *
   * When {@link #config-store} is provided, any filters in the store will
   * be automatically added and do not need to be provided explicitly.
   *
   * Example:
   * ```javascript
   * filters: [{
   *     // Filter properties should exist among field names configured
   *     // via `fields` or `store`
   *     property: 'age',
   *     operator: '<',
   *     value: 30
   * },{
   *     property: 'title',
   *     operator: 'startsWith',
   *     value: 'Director'
   * }]
   * ```
   *
   * @config
   * @type {CollectionFilterConfig[]}
   */
  filters: [],
  /**
   * Dictionary of {@link Core.widget.FieldFilterPicker#typedef-FieldOption} representing the fields against which filters can be defined,
   * keyed by field name.
   *
   * If filtering a {@link Grid.view.Grid}, consider using {@link Grid.widget.GridFieldFilterPicker}, which can be configured
   * with an existing {@link Grid.view.Grid} instead of, or in combination with, defining fields manually.
   *
   * Example:
   * ```javascript
   * fields: {
   *     // Allow filters to be defined against the 'age' and 'role' fields in our data
   *     age  : { text: 'Age', type: 'number' },
   *     role : { text: 'Role', type: 'string' }
   * }
   * ```
   *
   * @config {Object<String,FieldOption>}
   */
  fields: null,
  /**
   * Whether the picker group is disabled.
   *
   * @config {Boolean}
   * @default
   */
  disabled: false,
  /**
   * Whether the picker group is read-only.
   *
   * Example:
   * fields: [
   *    { name: 'age', type: 'number' },
   *    { name: 'title', type: 'string' }
   * ]
   *
   * @config {Boolean}
   * @default
   */
  readOnly: false,
  layout: "vbox",
  /**
   * The {@link Core.data.Store} whose records will be filtered. The store's {@link Core.data.Store#property-modelClass}
   * will be used to determine field types.
   *
   * This store will be kept in sync with the filters defined in the picker group, and new filters added to the store
   * via other means will appear in this filter group when they are able to be modified by it. (Some types of filters,
   * like arbitrary filter functions, cannot be managed through this widget.)
   *
   * As a corollary, multiple `FieldFilterPickerGroup`s configured with the same store will stay in sync, showing the
   * same filters as the store's filters change.
   *
   * @config {Core.data.Store}
   */
  store: null,
  /**
   * When `limitToProperty` is set to the name of an available field (as specified either
   * explicitly in {@link #config-fields} or implicitly in the
   * {@link #config-store}'s model), it has the following effects:
   *
   * - the picker group will only show filters defined on the specified property
   * - it will automatically set the `property` to the specified property for all newly added
   *   filters where the property is not already set
   * - the property selector is made read-only
   *
   * @config {String}
   */
  limitToProperty: null,
  /**
   * Optional CSS class to apply to the value field(s).
   *
   * @config {String}
   * @private
   */
  valueFieldCls: null,
  /**
   * Show a button at the bottom of the group that adds a new, blank filter to the group.
   *
   * @config {Boolean}
   * @default
   */
  showAddFilterButton: true,
  /**
   * Optional predicate that returns whether a given filter can be deleted. When `canDeleteFilter` is provided,
   * it will be called for each filter and will not show the delete button for those for which the
   * function returns `false`.
   *
   * @config {Function}
   * @param {*} filter  filter for checking
   * @returns {Boolean} truthy value if filter can be deleted
   */
  canDeleteFilter: null,
  /**
   * Optional function that returns {@link Core.widget.FieldFilterPicker} configuration properties for
   * a given filter. When `getFieldFilterPickerConfig` is provided, it will be called for each filter and the returned
   * object will be merged with the configuration properties for the individual
   * {@link Core.widget.FieldFilterPicker} representing that filter.
   *
   * The supplied function should accept a single argument, the {@link Core.util.CollectionFilter} whose picker
   * is being created.
   *
   * @config {Function}
   * @param {CollectionFilterConfig} filter The filter to be displayed in the FieldFilterPicker being configured
   * @returns {FieldFilterPickerConfig}
   */
  getFieldFilterPickerConfig: null,
  /**
   * Optional predicate that returns whether a given filter can be managed by this widget. When `canManageFilter`
   * is provided, it will be used to decide whether to display filters found in the configured
   * {@link #config-store}.
   *
   * @config {Function}
   * @param {*} filter  filter for checking
   * @returns {Boolean} truthy value if filter can be managed
   */
  canManageFilter: null,
  /**
   * Sets the text displayed in the 'add filter' button if one is present.
   *
   * @config {String}
   */
  addFilterButtonText: null,
  /**
   * Whether to raise {@link #event-change} events as the user types into a value field. If `false`,
   * {@link #event-change} events will be raised only when the value input field's own `change` event
   * occurs, for example on field blur.
   *
   * @config {Boolean}
   * @default
   */
  triggerChangeOnInput: true,
  /**
   * Optional array of field names that are allowed as selectable properties for filters.
   * This should be a subset of the field names found in the {@link #config-store}. When supplied,
   * only the named fields will be shown in the property selector combo.
   *
   * @config {String[]}
   */
  allowedFieldNames: null,
  /**
   * @private
   */
  items: {
    pickers: {
      type: "container",
      layout: "vbox",
      scrollable: true,
      items: {}
    },
    addFilterButton: {
      type: "button",
      text: _FieldFilterPickerGroup.addFilterButtonDefaultText,
      cls: `b-${_FieldFilterPickerGroup.type}-add-button`,
      hidden: true
    }
  },
  /**
   * When specified, overrides the built-in list of available operators. See
   * {@link Core.widget.FieldFilterPicker#config-operators}.
   *
   * @config {Object}
   */
  operators: null,
  /**
   * The date format string used to display dates when using the 'is one of' / 'is not one of' operators with a date
   * field. Defaults to the current locale's `FieldFilterPicker.dateFormat` value.
   *
   * @config {String}
   * @default
   */
  dateFormat: "L{FieldFilterPicker.dateFormat}"
});
// endregion
__publicField(_FieldFilterPickerGroup, "childPickerType", "fieldfilterpicker");
var FieldFilterPickerGroup = _FieldFilterPickerGroup;
FieldFilterPickerGroup.initClass();
FieldFilterPickerGroup._$name = "FieldFilterPickerGroup";

// ../Core/lib/Core/widget/FileField.js
var FileField = class extends Field {
  static get configurable() {
    return {
      /**
       * Set to `true` to allow picking multiple files. Note that when set to a truthy value,
       * the field is set to accept multiple files, but the value returned will be
       * an empty string since this is what is rendered into the HTML.
       * @config {Boolean}
       * @default
       */
      multiple: null,
      /**
       * Comma-separated list of file extensions or MIME type to accept. E.g.
       * ".jpg,.png,.doc" or "image/*". Null by default, allowing all files.
       * @config {String}
       */
      accept: null,
      inputType: "file",
      attributes: ["multiple", "accept"]
    };
  }
  /**
   * Returns list of selected files
   * @property {FileList}
   * @readonly
   */
  get files() {
    return this.input.files;
  }
  /**
   * Opens browser file picker
   * @internal
   */
  pickFile() {
    this.input.click();
  }
  get multiple() {
    return this._multiple ? "" : null;
  }
  /**
   * Clears field value
   */
  clear() {
    this.input.value = null;
  }
  triggerChange(event) {
    this.triggerFieldChange({
      event,
      value: this.input.value,
      oldValue: this._lastValue,
      userAction: true,
      valid: true
    });
  }
};
__publicField(FileField, "$name", "FileField");
__publicField(FileField, "type", "filefield");
FileField.initClass();
FileField._$name = "FileField";

// ../Core/lib/Core/widget/FilePicker.js
var FilePicker = class extends Container {
  construct(config = {}) {
    const me = this;
    config.items = [
      Object.assign({
        type: "button",
        ref: "fileButton",
        text: "L{FilePicker.file}",
        localeClass: this
      }, config.buttonConfig),
      Object.assign({
        type: "filefield",
        ref: "fileField",
        style: "display: none"
      }, config.fileFieldConfig)
    ].concat(config.items || []);
    super.construct(config);
    me.button.ion({
      click: me.onButtonClick,
      thisObj: me
    });
    me.fileField.ion({
      change: me.onFileFieldChange,
      thisObj: me
    });
    me._thisIsAUsedExpression(me.fileTip);
  }
  get button() {
    return this.widgetMap.fileButton;
  }
  get fileField() {
    return this.widgetMap.fileField;
  }
  /**
   * List of selected files
   * @property {FileList}
   * @readonly
   */
  get files() {
    return this.fileField.files;
  }
  get fileTip() {
    const me = this;
    return me._fileTip || (me._fileTip = new Tooltip({
      cls: "b-file-tip",
      forElement: me.button.element,
      showOnHover: true,
      align: "b-t",
      scrollAction: "realign",
      internalListeners: {
        beforeshow() {
          const tip = this, files = me.files;
          if (files && files.length) {
            tip.html = `${Array.from(files).map((file) => StringHelper.encodeHtml(file.name)).join("<br>")}`;
            return true;
          }
          return false;
        }
      }
    }));
  }
  /**
   * Clears field
   */
  clear() {
    const me = this;
    me.fileField.clear();
    me.updateBadge();
    me.trigger("clear");
  }
  onButtonClick({ event }) {
    const me = this;
    me.fileField.pickFile();
    event.preventDefault();
  }
  onFileFieldChange({ valid }) {
    const me = this;
    me.updateBadge();
    me.triggerFieldChange({ files: me.files, valid });
  }
  updateShowBadge() {
    if (!this.isConfiguring) {
      this.updateBadge();
    }
  }
  updateBadge() {
    this.button.badge = this.showBadge && this.files.length || "";
  }
};
__publicField(FilePicker, "$name", "FilePicker");
__publicField(FilePicker, "type", "filepicker");
__publicField(FilePicker, "configurable", {
  /**
   * The name of the property to set when a single value is to be applied to this FilePicker. Such as when used
   * in a grid WidgetColumn, this is the property to which the column's `field` is applied.
   * @config {String}
   * @default
   * @category Misc
   */
  defaultBindProperty: "value",
  /**
   * Fires after user closes file picker dialog.
   * @event change
   * @param {FileList} files List of picked files
   */
  /**
   * Fires when field is cleared with {@link #function-clear} method
   * @event clear
   */
  /**
   * Wrapper button config object. See {@link Core/widget/Button} for list of available configs.
   * @config {ButtonConfig}
   */
  buttonConfig: null,
  /**
   * Underlying field config object. See {@link Core/widget/FileField} for list of available configs.
   * @config {FileFieldConfig}
   */
  fileFieldConfig: null,
  /**
   * Set to `false` to hide the badge indicating the number of files selected
   * @prp {Boolean}
   * @default true
   */
  showBadge: true
});
FilePicker.initClass();
FilePicker._$name = "FilePicker";

// ../Core/lib/Core/widget/FilterField.js
var FilterField = class extends TextField {
  static get configurable() {
    return {
      /**
       * The model field name to filter by. Can optionally be replaced by {@link #config-filterFunction}
       * @config {String}
       * @category Filtering
       */
      field: null,
      /**
       * The store to filter.
       * @config {Core.data.Store}
       * @category Filtering
       */
      store: null,
      /**
       * Optional filter function to be called with record and value as parameters for store filtering.
       * ```javascript
       * {
       *     type           : 'filterfield',
       *     store          : myStore,
       *     filterFunction : (record, value)  => {
       *        return record.text.includes(value);
       *     }
       * }
       * ```
       *
       * @config {Function}
       * @param {Core.data.Model} record Record for comparison
       * @param {String} value Value to compare with
       * @returns {Boolean} Returns `true` if record matches comparison requirements
       *
       * @category Filtering
       */
      filterFunction: null,
      clearable: true,
      revertOnEscape: true,
      ignoreParentReadOnly: true,
      keyStrokeChangeDelay: 100,
      internalListeners: {
        change: "onInternalChange"
      }
    };
  }
  updateValue(value2, old) {
    super.updateValue(value2, old);
    if (value2 && this.isConfiguring) {
      this.onInternalChange({ value: value2 });
    }
  }
  onInternalChange({ value: value2 }) {
    const { store, field, filterFunction } = this;
    if (store) {
      const filterId = `${field || this.id}-Filter`;
      if (value2.length === 0) {
        store.removeFilter(filterId);
      } else {
        let filterBy;
        if (filterFunction) {
          filterBy = (record) => filterFunction(record, value2);
        } else {
          value2 = value2.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
          filterBy = (record) => {
            var _a5;
            return (_a5 = record.getValue(field)) == null ? void 0 : _a5.match(new RegExp(value2, "i"));
          };
        }
        store.filter({
          id: filterId,
          filterBy
        });
      }
    }
  }
};
__publicField(FilterField, "$name", "FilterField");
__publicField(FilterField, "type", "filterfield");
FilterField.initClass();
FilterField._$name = "FilterField";

// ../Core/lib/Core/widget/MessageDialog.js
var items = [
  {
    ref: "cancelButton",
    cls: "b-messagedialog-cancelbutton b-gray",
    text: "L{Object.Cancel}",
    onClick: "up.onCancelClick"
  },
  {
    ref: "okButton",
    cls: "b-messagedialog-okbutton b-raised b-blue",
    text: "L{Object.Ok}",
    onClick: "up.onOkClick"
  }
];
if (BrowserHelper.isWindows) {
  items.reverse();
}
var _MessageDialogConstructor = class _MessageDialogConstructor extends Popup {
  static get configurable() {
    return {
      ui: "plain",
      centered: true,
      modal: true,
      hidden: true,
      autoShow: false,
      closeAction: "hide",
      title: "\xA0",
      lazyItems: {
        $config: ["lazy"],
        value: [{
          cls: "b-messagedialog-message",
          ref: "message"
        }, {
          type: "textfield",
          cls: "b-messagedialog-input",
          ref: "input"
        }]
      },
      showClass: null,
      bbar: {
        overflow: null,
        items
      }
    };
  }
  construct() {
    this.okButton = this.yesButton = 1;
    this.cancelButton = 3;
    super.construct(...arguments);
  }
  // Protect from queryAll -> destroy
  destroy() {
  }
  /**
   * Shows a confirm dialog with "Ok" and "Cancel" buttons. The returned promise resolves passing the button identifier
   * of the button that was pressed ({@link #property-okButton} or {@link #property-cancelButton}).
   * @function confirm
   * @param {Object} options An options object for what to show.
   * @param {Boolean} [options.modal=true] Set to `false` to not have the dialog be modal
   * @param {String} [options.title] The title to show in the dialog header.
   * @param {String} [options.message] The message to show in the dialog body.
   * @param {String} [options.rootElement] The root element of this widget, defaults to document.body. Use this
   * if you use the MessageDialog inside a web component ShadowRoot
   * @param {String|ButtonConfig} [options.cancelButton] A text or a config object to apply to the Cancel button.
   * @param {String|ButtonConfig} [options.okButton] A text or config object to apply to the OK button.
   * @returns {Promise} A promise which is resolved when the dialog is closed
   */
  async confirm() {
    return this.showDialog("confirm", ...arguments);
  }
  /**
   * Shows an alert popup with a message. The returned promise resolves when the button is clicked.
   * @function alert
   * @param {Object} options An options object for what to show.
   * @param {Boolean} [options.modal=true] Set to `false` to not have the dialog be modal
   * @param {String} [options.title] The title to show in the dialog header.
   * @param {String} [options.message] The message to show in the dialog body.
   * @param {String} [options.rootElement] The root element of this widget, defaults to document.body. Use this
   * if you use the MessageDialog inside a web component ShadowRoot
   * @param {String|ButtonConfig} [options.okButton] A text or config object to apply to the OK button.
   * @returns {Promise} A promise which is resolved when the dialog is closed
   */
  async alert() {
    return this.showDialog("alert", ...arguments);
  }
  /**
   * Shows a popup with a basic {@link Core.widget.TextField} along with a message. The returned promise resolves when
   * the dialog is closed and yields an Object with a `button` ({@link #property-okButton} or {@link #property-cancelButton})
   * and a `text` property with the text the user provided
   * @function prompt
   * @param {Object} options An options object for what to show.
   * @param {Boolean} [options.modal=true] Set to `false` to not have the dialog be modal
   * @param {String} [options.title] The title to show in the dialog header.
   * @param {String} [options.message] The message to show in the dialog body.
   * @param {String} [options.rootElement] The root element of this widget, defaults to document.body. Use this
   * if you use the MessageDialog inside a web component ShadowRoot
   * @param {TextFieldConfig} [options.textField] A config object to apply to the TextField.
   * @param {String|ButtonConfig} [options.cancelButton] A text or a config object to apply to the Cancel button.
   * @param {String|ButtonConfig} [options.okButton] A text or config object to apply to the OK button.
   * @returns {Promise} A promise which is resolved when the dialog is closed. The promise yields an Object with
   * a `button` ({@link #property-okButton} or {@link #property-cancelButton}) and a `text` property with the text the
   * user provided
   */
  async prompt({
    textField
  }) {
    const field = this.widgetMap.input;
    Widget.reconfigure(field, textField);
    field.value = textField == null ? void 0 : textField.value;
    return this.showDialog("prompt", ...arguments);
  }
  showDialog(mode, {
    message = "",
    title = "",
    cancelButton,
    okButton,
    align,
    modal,
    rootElement = document.body
  }) {
    const me = this;
    me.rootElement = rootElement;
    me.getConfig("lazyItems");
    me.title = me.optionalL(title);
    me.widgetMap.message.html = me.optionalL(message);
    me.showClass = `b-messagedialog-${mode}`;
    me.modal = typeof modal === "boolean" ? modal : true;
    me.centered = !align;
    if (okButton) {
      okButton = typeof okButton === "string" ? { text: okButton } : okButton;
    }
    if (cancelButton) {
      cancelButton = typeof cancelButton === "string" ? { text: cancelButton } : cancelButton;
    }
    okButton = Object.assign({}, me.widgetMap.okButton.initialConfig, okButton);
    cancelButton = Object.assign({}, me.widgetMap.cancelButton.initialConfig, cancelButton);
    okButton.text = me.optionalL(okButton.text);
    cancelButton.text = me.optionalL(cancelButton.text);
    Widget.reconfigure(me.widgetMap.okButton, okButton);
    Widget.reconfigure(me.widgetMap.cancelButton, cancelButton);
    me.show({ align });
    return me.promise = new Promise((resolve) => {
      me.resolve = resolve;
    });
  }
  show() {
    const activeElement = DomHelper.getActiveElement(this.element);
    this.owner = this.element.contains(activeElement) ? null : _MessageDialogConstructor.fromElement(document.activeElement);
    return super.show(...arguments);
  }
  updateShowClass(showClass, oldShowClass) {
    const { classList } = this.element;
    if (oldShowClass) {
      classList.remove(oldShowClass);
    }
    if (showClass) {
      classList.add(showClass);
    }
  }
  doResolve(value2) {
    const me = this, { resolve } = me;
    if (resolve) {
      const isPrompt = me.showClass === "b-messagedialog-prompt";
      if (isPrompt && value2 === me.okButton && !me.widgetMap.input.isValid) {
        return;
      }
      me.resolve = me.reject = me.promise = null;
      resolve(isPrompt ? {
        button: value2,
        text: me.widgetMap.input.value
      } : value2);
      me.hide();
    }
  }
  onInternalKeyDown(event) {
    if (event.key === "Escape") {
      event.stopImmediatePropagation();
      this.onCancelClick();
    } else if (event.key === "Enter" && !event.target.closest(".b-button")) {
      event.stopImmediatePropagation();
      event.preventDefault();
      this.onOkClick();
    }
    super.onInternalKeyDown(event);
  }
  onOkClick() {
    this.doResolve(MessageDialog.okButton);
  }
  onCancelClick() {
    this.doResolve(MessageDialog.cancelButton);
  }
};
__publicField(_MessageDialogConstructor, "$name", "MessageDialog");
__publicField(_MessageDialogConstructor, "type", "messagedialog");
var MessageDialogConstructor = _MessageDialogConstructor;
MessageDialogConstructor.initClass();
var MessageDialog = new Proxy({}, {
  get(target, prop) {
    const instance = target.instance || (target.instance = new MessageDialogConstructor({
      rootElement: document.body
    })), result = instance[prop];
    return typeof result === "function" ? result.bind(instance) : result;
  }
});
var MessageDialog_default = MessageDialog;

// ../Core/lib/Core/widget/PagingToolbar.js
var PagingToolbar = class extends Toolbar {
  static get defaultConfig() {
    return {
      defaults: {
        localeClass: this
      },
      items: {
        firstPageButton: {
          onClick: "up.onFirstPageClick",
          icon: "b-icon-first",
          weight: 100,
          tooltip: "L{PagingToolbar.firstPage}"
        },
        previousPageButton: {
          onClick: "up.onPreviousPageClick",
          icon: "b-icon-previous",
          weight: 110,
          tooltip: "L{PagingToolbar.prevPage}"
        },
        pageNumber: {
          type: "numberfield",
          label: "L{page}",
          min: 1,
          max: 1,
          triggers: null,
          onChange: "up.onPageNumberChange",
          highlightExternalChange: false,
          weight: 120
        },
        pageCount: {
          type: "widget",
          cls: "b-pagecount b-toolbar-text",
          weight: 130
        },
        nextPageButton: {
          onClick: "up.onNextPageClick",
          icon: "b-icon-next",
          weight: 140,
          tooltip: "L{PagingToolbar.nextPage}"
        },
        lastPageButton: {
          onClick: "up.onLastPageClick",
          icon: "b-icon-last",
          weight: 150,
          tooltip: "L{PagingToolbar.lastPage}"
        },
        separator: {
          type: "widget",
          cls: "b-toolbar-separator",
          weight: 151
        },
        reloadButton: {
          onClick: "up.onReloadClick",
          icon: "b-icon-reload",
          weight: 160,
          tooltip: "L{PagingToolbar.reload}"
        },
        spacer: {
          type: "widget",
          cls: "b-toolbar-fill",
          weight: 161
        },
        dataSummary: {
          type: "widget",
          cls: "b-toolbar-text",
          weight: 170
        }
      }
    };
  }
  // Retrieve store from grid when "assigned" to it
  set parent(parent) {
    super.parent = parent;
    if (!this.store) {
      this.store = parent.store;
    }
  }
  get parent() {
    return super.parent;
  }
  changeStore(store) {
    var _a5;
    if (typeof store === "string") {
      return (_a5 = this.parent) == null ? void 0 : _a5[store];
    }
    return store;
  }
  updateStore(store) {
    const me = this;
    me.detachListeners("store");
    if (store) {
      store.ion({
        name: "store",
        beforerequest: "onStoreBeforeRequest",
        afterrequest: "onStoreChange",
        change: "onStoreChange",
        thisObj: me
      });
      if (store.isLoading) {
        me.onStoreBeforeRequest();
      }
    }
  }
  onStoreBeforeRequest() {
    this.eachWidget((w) => w.disable());
  }
  updateLocalization() {
    this.updateSummary();
    super.updateLocalization();
  }
  updateSummary() {
    const me = this, { pageCount, dataSummary } = me.widgetMap;
    let count = 0, lastPage = 0, start = 0, end = 0, allCount = 0;
    if (me.store) {
      const { store } = me, { pageSize, currentPage } = store;
      count = store.count;
      lastPage = store.lastPage;
      allCount = store.totalCount;
      start = Math.max(0, (currentPage - 1) * pageSize + 1);
      end = Math.min(allCount, start + pageSize - 1);
    }
    pageCount.html = me.L("L{pageCountTemplate}")({ lastPage });
    dataSummary && (dataSummary.html = count ? me.L("L{summaryTemplate}")({ start, end, allCount }) : me.L("L{noRecords}"));
  }
  onStoreChange() {
    const me = this, { widgetMap, store } = me, { count, lastPage, currentPage } = store, {
      pageNumber,
      pageCount,
      firstPageButton,
      previousPageButton,
      nextPageButton,
      lastPageButton,
      dataSummary
    } = widgetMap;
    me.eachWidget((w) => w.enable());
    pageNumber.value = currentPage;
    pageNumber.max = lastPage;
    dataSummary && (dataSummary.disabled = !count);
    pageNumber.disabled = pageCount.disabled = !count;
    firstPageButton.disabled = previousPageButton.disabled = currentPage <= 1 || !count;
    nextPageButton.disabled = lastPageButton.disabled = currentPage >= lastPage || !count;
    me.updateSummary();
  }
  onPageNumberChange({ value: value2 }) {
    if (this.store.currentPage !== value2) {
      this.store.loadPage(value2);
    }
  }
  onFirstPageClick() {
    this.store.loadPage(1);
  }
  onPreviousPageClick() {
    this.store.previousPage();
  }
  onNextPageClick() {
    this.store.nextPage();
  }
  onLastPageClick() {
    this.store.loadPage(this.store.lastPage);
  }
  onReloadClick() {
    this.store.loadPage(this.store.currentPage);
  }
};
__publicField(PagingToolbar, "$name", "PagingToolbar");
__publicField(PagingToolbar, "type", "pagingtoolbar");
__publicField(PagingToolbar, "configurable", {
  /**
   * The {@link Core.data.Store} that this PagingToolbar is to control. If set to a string value, that string
   * value should represent the property name of the Store's reference on this toolbar's parent component.
   * By default, the toolbar will control the Store found at the parent components 'store' property.
   * @config {Core.data.Store|String}
   */
  store: null
});
PagingToolbar.initClass();
PagingToolbar._$name = "PagingToolbar";

// ../Core/lib/Core/widget/PasswordField.js
var PasswordField = class extends Field {
  construct(config = {}) {
    config.inputType = "password";
    super.construct(...arguments);
    this.element.classList.add("b-textfield");
  }
};
__publicField(PasswordField, "$name", "PasswordField");
__publicField(PasswordField, "type", "passwordfield");
__publicField(PasswordField, "alias", "password");
PasswordField.initClass();
PasswordField._$name = "PasswordField";

// ../Core/lib/Core/widget/Slider.js
var arrowKeys = {
  ArrowUp: 1,
  ArrowDown: 1,
  ArrowLeft: 1,
  ArrowRight: 1
};
var Slider = class extends Widget {
  static get configurable() {
    return {
      /**
       * Get input element.
       * @readonly
       * @member {HTMLInputElement} input
       */
      /**
       * Get/set text. Appends value if Slider.showValue is true
       * @member {String} text
       */
      /**
       * Slider label text
       * @config {String}
       */
      text: null,
      /**
       * Show value in label (appends in () if text is set)
       * @config {Boolean}
       * @default
       */
      showValue: true,
      /**
       * Show the slider value in a tooltip
       * @config {Boolean}
       * @default
       */
      showTooltip: false,
      /**
       * Get/set min value
       * @member {Number} min
       */
      /**
       * Minimum value
       * @config {Number}
       * @default
       */
      min: 0,
      /**
       * Get/set max value
       * @member {Number} max
       */
      /**
       * Maximum value
       * @config {Number}
       * @default
       */
      max: 100,
      /**
       * Get/set step size
       * @member {Number} step
       */
      /**
       * Step size
       * @config {Number}
       * @default
       */
      step: 1,
      /**
       * Get/set value
       * @member {Number} value
       */
      /**
       * Initial value.
       * `String` value allows to link value by reference name.
       *
       * Example:
       * ```javascript
       * new Slider({
       *     text      : 'Tick height',
       *     value     : 100,
       * }
       *
       * new Slider({
       *     text      : 'Tick height',
       *     value     : 'up.tickSize',
       * }
       * ```
       *
       * @config {Number|String}
       */
      value: 50,
      /**
       * Unit to display next to the value, when configured with `showValue : true`
       * @config {String}
       * @default
       */
      unit: null,
      // The value is set in the Light theme. The Material theme will have different value.
      thumbSize: 20,
      /**
       * A config object for the tooltip to show while hovering the slider.
       * @config {TooltipConfig}
       */
      tooltip: {
        $config: ["lazy", "nullify"],
        value: {
          type: "tooltip",
          align: "b-t",
          anchor: false,
          // No anchor displayed since thumbSize is different for different themes
          axisLock: true,
          autoClose: false
        }
      },
      localizableProperties: ["text"],
      /**
       * By default, the {@link #event-change} event is fired when a change gesture is completed, ie: on
       * the mouse up gesture of a drag.
       *
       * Configure this as `true` to fire the {@link #event-change} event as the value changes *during* a drag.
       * @prp {Boolean}
       */
      triggerChangeOnInput: null,
      defaultBindProperty: "value",
      twinSyncConfigs: ["max", "min", "step", "value"],
      /**
       * Label, prepended to field
       * @config {String}
       * @category Label
       */
      label: null,
      labelWidth: null
    };
  }
  //endregion
  //region Init
  compose() {
    const { id: id2, min: min4, max: max3, showValue, step, text, value: value2, unit = "", disabled, readOnly, label } = this, inputId = `${id2}-input`, hasText = Boolean(text || showValue), valueText = step < 1 ? value2.toFixed(2) : value2;
    return {
      class: {
        "b-has-field-label": label,
        "b-has-label": hasText,
        "b-label-before": hasText,
        "b-text": hasText,
        "b-disabled": disabled
      },
      children: {
        inputLabel: label ? {
          tag: "label",
          for: inputId,
          text: label,
          style: {
            width: this.labelWidth
          }
        } : null,
        input: {
          tag: "input",
          type: "range",
          id: inputId,
          reference: "input",
          [disabled ? "disabled" : ""]: disabled,
          [readOnly ? "readOnly" : ""]: readOnly,
          min: min4,
          max: max3,
          step,
          value: value2,
          // eslint-disable-next-line bryntum/no-listeners-in-lib
          listeners: {
            input: "onInternalInput",
            change: "onInternalChange",
            mouseover: "onInternalMouseOver",
            mouseout: "onInternalMouseOut"
          }
        },
        valueLabel: hasText ? {
          tag: "label",
          class: "b-slider-value",
          for: inputId,
          text: showValue ? text ? `${text} (${valueText}${unit})` : valueText + unit : text
        } : null
      }
    };
  }
  get focusElement() {
    return this.input;
  }
  get percentProgress() {
    return (this.value - this.min) / (this.max - this.min) * 100;
  }
  //endregion
  //region Events
  /**
   * Fired while slider thumb is being dragged.
   * @event input
   * @param {Core.widget.Slider} source The slider
   * @param {Number} value The value
   */
  /**
   * Fired after the slider value changes (on mouse up following slider interaction).
   * @event change
   * @param {Number} value The value
   * @param {Boolean} userAction Triggered by user taking an action (`true`) or by setting a value (`false`)
   * @param {Core.widget.Slider} source The slider
   */
  /* break from doc comment */
  onInternalKeyDown(e) {
    if (!this.readOnly && arrowKeys[e.key]) {
      e.stopImmediatePropagation();
    }
  }
  onInternalChange() {
    this.updateUI();
    this.triggerChange(true);
    this.trigger("action", { value: this.value });
  }
  onInternalInput() {
    const me = this;
    if (me.readOnly) {
      me.input.value = me.value;
      return;
    }
    me.value = me.step < 1 ? parseFloat(me.input.value) : parseInt(me.input.value, 10);
    me.trigger("input", { value: me.value });
    if (me.triggerChangeOnInput) {
      me.triggerChange(me);
    }
  }
  onInternalMouseOver() {
    var _a5;
    const me = this, thumbPosition = me.rtl ? 100 - me.percentProgress : me.percentProgress;
    (_a5 = me.tooltip) == null ? void 0 : _a5.showBy({
      target: Rectangle.from(me.input).inflate(me.thumbSize / 2, -me.thumbSize / 2),
      align: `b-t${Math.round(thumbPosition)}`
    });
  }
  onInternalMouseOut() {
    var _a5;
    (_a5 = this.tooltip) == null ? void 0 : _a5.hide();
  }
  triggerChange(userAction) {
    this.triggerFieldChange({
      value: this.value,
      valid: true,
      userAction
    });
  }
  //endregion
  //region Config Handling
  // max
  updateMax(max3) {
    const me = this;
    if (me.input && me._value > max3) {
      me.value = max3;
      me.trigger("input", { value: me.value });
    }
  }
  // min
  updateMin(min4) {
    const me = this;
    if (me.input && me._value < min4) {
      me.value = min4;
      me.trigger("input", { value: me.value });
    }
  }
  // tooltip
  changeTooltip(config, existingTooltip) {
    var _a5;
    if (config) {
      config.owner = this;
    }
    const { value: value2 } = this, valueText = this.step < 1 ? value2.toFixed(2) : value2;
    return this.showTooltip ? Tooltip.reconfigure(existingTooltip, config, {
      owner: this,
      defaults: {
        forElement: this.input,
        html: `${valueText}${(_a5 = this.unit) != null ? _a5 : ""}`
      }
    }) : null;
  }
  changeValue(value2) {
    const me = this, { min: min4, step } = me;
    value2 = Math.min(Math.max(value2, min4), me.max);
    if (value2 > min4) {
      return min4 + ObjectHelper.roundTo(value2 - min4, step);
    }
    return ObjectHelper.roundTo(value2, step);
  }
  updateValue(value2) {
    var _a5;
    const me = this, { input, _tooltip } = me, valueText = me.step < 1 ? value2.toFixed(2) : value2;
    if (input && input.value !== String(value2)) {
      input.value = value2;
      me.triggerChange(false);
    }
    me.updateUI();
    if (_tooltip) {
      _tooltip.html = `${valueText}${(_a5 = me.unit) != null ? _a5 : ""}`;
    }
  }
  //endregion
  //region Util
  updateUI() {
    var _a5, _b2;
    const me = this;
    ((_a5 = me._tooltip) == null ? void 0 : _a5.isVisible) && ((_b2 = me._tooltip) == null ? void 0 : _b2.alignTo({
      target: Rectangle.from(me.input).inflate(me.thumbSize / 2, -me.thumbSize / 2),
      align: `b-t${Math.round(me.percentProgress)}`
    }));
  }
  //endregion
};
//region Config
__publicField(Slider, "$name", "Slider");
__publicField(Slider, "type", "slider");
Slider.initClass();
Slider._$name = "Slider";

// ../Core/lib/Core/widget/SlideToggle.js
var SlideToggle = class extends Checkbox {
  static get properties() {
    return {
      toggledCls: "b-slidetoggle-checked"
    };
  }
  construct(config) {
    if (config.checked) {
      config.cls = DomClassList.from(config.cls) || {};
      config.cls[this.constructor.properties.toggledCls] = 1;
    }
    super.construct(config);
  }
  get innerElements() {
    const innerEls = super.innerElements;
    innerEls.splice(1, 0, this.toggleElement);
    if (this.text) {
      innerEls[innerEls.length - 1].class = "b-slidetoggle-label";
    } else {
      innerEls.pop();
    }
    return innerEls;
  }
  get toggleElement() {
    return {
      class: "b-slidetoggle-toggle",
      reference: "slideToggle",
      children: [
        {
          class: "b-slidetoggle-thumb",
          reference: "slideThumb"
        }
      ]
    };
  }
  internalOnChange() {
    super.internalOnChange();
    this.element.classList[this.value ? "add" : "remove"](this.toggledCls);
  }
};
__publicField(SlideToggle, "$name", "SlideToggle");
__publicField(SlideToggle, "type", "slidetoggle");
SlideToggle.initClass();
SlideToggle._$name = "SlideToggle";

// ../Core/lib/Core/widget/Splitter.js
var classesHV = ["b-horizontal", "b-vertical"];
var hasFlex = (el) => DomHelper.getStyleValue(el.parentElement, "display") === "flex" && (parseInt(DomHelper.getStyleValue(el, "flex-basis"), 10) || parseInt(DomHelper.getStyleValue(el, "flex-grow"), 10));
var verticality = {
  horizontal: false,
  vertical: true
};
var Splitter = class extends Widget {
  static get configurable() {
    return {
      /**
       * Fired when a drag starts
       * @event dragStart
       * @param {Core.widget.Splitter} source The Splitter
       * @param {MouseEvent|TouchEvent} event The DOM event
       */
      /**
       * Fired while dragging
       * @event drag
       * @param {Core.widget.Splitter} source The Splitter
       * @param {MouseEvent|TouchEvent} event The DOM event
       */
      /**
       * Fired after a drop
       * @event drop
       * @param {Core.widget.Splitter} source The Splitter
       * @param {MouseEvent|TouchEvent} event The DOM event
       */
      /**
       * Splitter orientation, see {@link #config-orientation}. When set to 'auto' then actually used orientation
       * can be retrieved using {@link #property-currentOrientation}.
       * @member {'auto'|'horizontal'|'vertical'} orientation
       * @readonly
       */
      /**
       * The splitter's orientation, configurable with 'auto', 'horizontal' or 'vertical'.
       *
       * 'auto' tries to determine the orientation by either checking the `flex-direction` of the parent element
       * or by comparing the positions of the closest sibling elements to the splitter. If they are above and
       * below 'horizontal' is used, if not it uses 'vertical'.
       *
       * ```
       * new Splitter({
       *    orientation : 'horizontal'
       * });
       * ```
       *
       * To receive the actually used orientation when configured with 'auto', see
       * {@link #property-currentOrientation}.
       *
       * @config {'auto'|'horizontal'|'vertical'}
       * @default
       */
      orientation: "auto",
      /**
       * Set to `true` to show the splitter's collapse/expand buttons, or to 'start' or 'end' to only show
       * buttons pointing to the previous or next element respectively.
       *
       * Setting to `false` will hide the buttons.
       *
       * @config {Boolean|'start'|'end'}
       * @default
       */
      showButtons: false,
      vertical: null,
      containerElement: {
        $config: "nullify",
        value: null
      },
      nextNeighbor: {
        $config: "nullify",
        value: null
      },
      previousNeighbor: {
        $config: "nullify",
        value: null
      }
    };
  }
  static get delayable() {
    return {
      syncState: "raf"
    };
  }
  //endregion
  //region Init & destroy
  doDestroy() {
    var _a5;
    (_a5 = this.mouseDetacher) == null ? void 0 : _a5.call(this);
    super.doDestroy();
  }
  //endregion
  //region Template & element
  compose() {
    return {
      class: {
        "b-splitter": 1
      },
      children: this.showButtons ? [
        BrowserHelper.isTouchDevice ? { className: "b-splitter-touch-area" } : null,
        {
          className: "b-splitter-inner",
          children: [
            {
              className: "b-splitter-buttons",
              reference: "splitterButtons",
              children: [
                {
                  className: "b-splitter-button-collapse",
                  reference: "collapseButton",
                  children: [
                    BrowserHelper.isTouchDevice ? { className: "b-splitter-button-touch-area" } : null,
                    {
                      tag: "i",
                      className: "b-icon b-icon-collapse-left b-splitter-collapse-arrow b-splitter-button-icon"
                    }
                  ]
                },
                {
                  className: "b-splitter-button-expand",
                  reference: "expandButton",
                  children: [
                    BrowserHelper.isTouchDevice ? { className: "b-splitter-button-touch-area" } : null,
                    {
                      tag: "i",
                      className: "b-icon b-icon-collapse-right b-splitter-expand-arrow b-splitter-button-icon"
                    }
                  ]
                }
              ]
            }
          ]
        }
      ] : null,
      // eslint-disable-next-line bryntum/no-listeners-in-lib
      listeners: {
        pointerdown: "onMouseDown",
        mouseenter: "onMouseEnter",
        mouseleave: "onMouseLeave",
        mousewheel: "onScroll",
        dblclick: "onDoubleClick",
        ...!BrowserHelper.supportsPointerEvents && {
          mousedown: "onMouseDown",
          touchstart: "onMouseDown"
        }
      }
    };
  }
  //endregion
  //region Orientation
  /**
   * Get actually used orientation, which is either the configured value for `orientation` or if configured with
   * 'auto' the currently used orientation.
   * @property {String}
   * @readonly
   */
  get currentOrientation() {
    return this.vertical ? "vertical" : "horizontal";
  }
  getSibling(next = true) {
    let { element } = this, result;
    while (!result && (element = element[`${next ? "next" : "previous"}ElementSibling`])) {
      if (!element.isConnected || DomHelper.isVisible(element)) {
        result = element;
      }
    }
    return result;
  }
  get nextWidget() {
    let { element } = this, result;
    while (!result && (element = element.nextElementSibling)) {
      result = Widget.fromElement(element, this.element.parentElement);
    }
    return result;
  }
  get previousWidget() {
    let { element } = this, result;
    while (!result && (element = element.previousElementSibling)) {
      result = Widget.fromElement(element, this.element.parentElement);
    }
    return result;
  }
  get nextSplitter() {
    let { element } = this, result;
    while (!result && (element = element == null ? void 0 : element.nextElementSibling)) {
      const widget = Widget.fromElement(element, this.element.parentElement);
      if ((widget == null ? void 0 : widget.type) === "splitter") {
        result = widget;
      }
    }
    return result;
  }
  get previousSplitter() {
    let { element } = this, result;
    while (!result && (element = element == null ? void 0 : element.previousElementSibling)) {
      const widget = Widget.fromElement(element, this.element.parentElement);
      if ((widget == null ? void 0 : widget.type) === "splitter") {
        result = widget;
      }
    }
    return result;
  }
  updateContainerElement(containerElement) {
    var _a5;
    const me = this;
    me.stateDetector = (_a5 = me.stateDetector) == null ? void 0 : _a5.disconnect();
    if (containerElement) {
      me.stateDetector = new MutationObserver(() => me.syncState());
      me.stateDetector.observe(containerElement, {
        attributes: true,
        // in case style changes flip our orientation (when == 'auto')
        childList: true
        // watch for our neighbors to render (so we can disable on hidden/collapsed state)
      });
    }
  }
  updateNextNeighbor(next) {
    this.watchNeighbor(next, "next");
  }
  updatePreviousNeighbor(previous) {
    this.watchNeighbor(previous, "previous");
  }
  watchNeighbor(neighbor, name) {
    this.detachListeners(name);
    neighbor == null ? void 0 : neighbor.ion({
      name,
      thisObj: this,
      collapse: "syncState",
      expand: "syncState",
      hide: "syncState",
      show: "syncState"
    });
  }
  updateOrientation() {
    this.syncState.now();
  }
  updateVertical(vertical) {
    var _a5;
    const classList = (_a5 = this.element) == null ? void 0 : _a5.classList;
    classList == null ? void 0 : classList.add(classesHV[vertical ? 1 : 0]);
    classList == null ? void 0 : classList.remove(classesHV[vertical ? 0 : 1]);
  }
  /**
   * Determine orientation when set to `'auto'` and detects neighboring widgets to monitor their hidden/collapsed
   * states.
   * @private
   */
  syncState(context) {
    var _a5, _b2, _c2, _d, _e, _f, _g;
    const me = this, {
      element,
      nextWidget,
      previousWidget,
      nextSplitter,
      previousSplitter
    } = me;
    let vertical = (_a5 = verticality[me.orientation]) != null ? _a5 : null;
    me.nextNeighbor = nextWidget;
    me.previousNeighbor = previousWidget;
    me.disabled = !me.showButtons && (nextWidget && (nextWidget.collapsible && nextWidget.collapsed || nextWidget.hidden) || previousWidget && (previousWidget.collapsible && previousWidget.collapsed || previousWidget.hidden));
    if (((_b2 = nextSplitter == null ? void 0 : nextSplitter.collapseState) == null ? void 0 : _b2.status) === "collapsed" || ((_c2 = previousSplitter == null ? void 0 : previousSplitter.collapseState) == null ? void 0 : _c2.status) === "expanded" || ((_d = me.collapseState) == null ? void 0 : _d.status) === "collapsed" || ((_e = me.collapseState) == null ? void 0 : _e.status) === "expanded") {
      me.element.classList.add("b-drag-disabled");
    } else {
      me.element.classList.remove("b-drag-disabled");
    }
    if ((context == null ? void 0 : context.type) === "expand" && me.showButtons) {
      if ((context == null ? void 0 : context.source) === previousWidget && ((_f = me.collapseState) == null ? void 0 : _f.status) === "collapsed" || (context == null ? void 0 : context.source) === nextWidget && ((_g = me.collapseState) == null ? void 0 : _g.status) === "expanded") {
        me.collapseState = null;
        element.classList.remove("b-drag-disabled", "b-show-buttons");
      }
      me.setSplitterButtonPosition();
      me.setButtonVisibility();
    } else if ((context == null ? void 0 : context.type) === "collapse" && me.showButtons) {
      if (context.source === previousWidget && !me.collapseState) {
        me.collapseState = {
          status: "collapsed"
        };
      } else if (context.source === nextWidget && !me.collapseState) {
        me.collapseState = {
          status: "expanded"
        };
      }
      element.classList.add("b-drag-disabled", "b-show-buttons");
      me.setSplitterButtonPosition();
      me.setButtonVisibility();
    }
    if (vertical !== null && nextWidget && previousWidget) {
      me.containerElement = null;
    } else {
      me.containerElement = element.parentElement;
      if (me.rendered && element.offsetParent) {
        const flexDirection = DomHelper.getStyleValue(element.parentElement, "flex-direction");
        if (flexDirection) {
          vertical = !flexDirection.startsWith("column");
        } else {
          const previous = element.previousElementSibling, next = element.nextElementSibling;
          if (!previous || !next) {
            return;
          }
          const prevRect = previous.getBoundingClientRect(), nextRect = next.getBoundingClientRect(), topMost = prevRect.top < nextRect.top ? prevRect : nextRect, bottomMost = topMost === nextRect ? prevRect : nextRect;
          vertical = topMost.top === bottomMost.top;
        }
      }
    }
    me.vertical = vertical;
  }
  //endregion
  //region Collapse/Expand
  setButtonVisibility() {
    var _a5, _b2, _c2, _d, _e, _f;
    const {
      showButtons,
      collapseState,
      collapseButton,
      expandButton,
      nextWidget,
      previousWidget,
      nextSplitter,
      previousSplitter
    } = this;
    if (showButtons === "start" || showButtons === "end") {
      if ((collapseState == null ? void 0 : collapseState.status) === "collapsed" || (collapseState == null ? void 0 : collapseState.status) === "expanded") {
        collapseButton.classList.toggle("b-disabled", collapseState.status === "collapsed");
        expandButton.classList.toggle("b-disabled", collapseState.status === "expanded");
      } else {
        collapseButton.classList.toggle("b-disabled", showButtons !== "start" && !(previousWidget == null ? void 0 : previousWidget.collapsible) || ((_a5 = previousSplitter == null ? void 0 : previousSplitter.collapseState) == null ? void 0 : _a5.status) === "expanded");
        expandButton.classList.toggle("b-disabled", showButtons !== "end" && !(nextWidget == null ? void 0 : nextWidget.collapsible) || ((_b2 = nextSplitter == null ? void 0 : nextSplitter.collapseState) == null ? void 0 : _b2.status) === "collapsed");
      }
    } else {
      collapseButton.classList.toggle("b-disabled", !showButtons || (collapseState == null ? void 0 : collapseState.status) === "collapsed" || ((_c2 = previousSplitter == null ? void 0 : previousSplitter.collapseState) == null ? void 0 : _c2.status) === "expanded");
      expandButton.classList.toggle("b-disabled", !showButtons || (collapseState == null ? void 0 : collapseState.status) === "expanded" || ((_d = nextSplitter == null ? void 0 : nextSplitter.collapseState) == null ? void 0 : _d.status) === "collapsed");
    }
    if ((collapseState == null ? void 0 : collapseState.status) !== "expanded" && ((_e = previousSplitter == null ? void 0 : previousSplitter.collapseState) == null ? void 0 : _e.status) === "collapsed") {
      previousSplitter.setButtonVisibility();
    } else if ((collapseState == null ? void 0 : collapseState.status) !== "collapsed" && ((_f = nextSplitter == null ? void 0 : nextSplitter.collapseState) == null ? void 0 : _f.status) === "expanded") {
      nextSplitter.setButtonVisibility();
    }
  }
  setSplitterButtonPosition(delta = 0) {
    const {
      vertical,
      splitterButtons,
      element: { parentElement }
    } = this;
    if (vertical) {
      const parentHeight = parentElement.clientHeight, parentScrollTop = parentElement.scrollTop + delta, newTop = Math.min(Math.max(0, parentScrollTop), parentElement.scrollHeight - parentHeight) + parentHeight / 2;
      splitterButtons.style.top = `${newTop}px`;
    } else {
      const parentWidth = parentElement.clientWidth, parentScrollLeft = parentElement.scrollLeft + delta, newLeft = Math.min(Math.max(0, parentScrollLeft), parentElement.scrollWidth - parentWidth) + parentWidth / 2;
      splitterButtons.style.left = `${newLeft}px`;
    }
  }
  restoreOriginalState() {
    const {
      nextNeighbor,
      previousNeighbor,
      collapseState,
      vertical
    } = this, prev = previousNeighbor ? previousNeighbor.element : this.getSibling(false), next = nextNeighbor ? nextNeighbor.element : this.getSibling();
    if (!collapseState) {
      return;
    }
    if (vertical) {
      if (collapseState.prevHasFlex) {
        prev.style.flexGrow = collapseState.prevFlexGrow;
      } else {
        prev.style.width = `${collapseState.prevWidth}px`;
      }
      if (collapseState.nextHasFlex) {
        next.style.flexGrow = collapseState.nextFlexGrow;
      } else {
        next.style.width = `${collapseState.nextWidth}px`;
      }
    } else {
      if (collapseState.prevHasFlex) {
        prev.style.flexGrow = collapseState.prevFlexGrow;
      } else {
        prev.style.height = `${collapseState.prevHeight}px`;
      }
      if (collapseState.nextHasFlex) {
        next.style.flexGrow = collapseState.nextFlexGrow;
      } else {
        next.style.height = `${collapseState.nextHeight}px`;
      }
    }
  }
  lockFlexedSiblings() {
    var _a5;
    const corrections = [];
    for (const child of this.element.parentElement.children) {
      if (hasFlex(child) && child !== this.element && ((_a5 = Widget.fromElement(child, this.element.parentElement)) == null ? void 0 : _a5.type) !== "splitter") {
        corrections.push({
          child,
          width: child.offsetWidth,
          height: child.offsetHeight
        });
      }
    }
    for (const correction of corrections) {
      correction.child.style.flexGrow = this.vertical ? correction.width : correction.height;
      correction.child.style.flexBasis = 0;
    }
  }
  //endregion
  //region Events
  onDoubleClick({ ctrlKey }) {
    const siblings = ctrlKey ? this.element.parentElement.querySelectorAll(":scope > :not(.b-splitter)") : [this.getSibling(false), this.getSibling()];
    siblings.forEach((e) => e && (e.style.flex = "1 1 0px"));
  }
  onMouseDown(event) {
    var _a5, _b2, _c2, _d, _e, _f;
    const me = this, { target } = event;
    if (target.closest(".b-splitter-button-collapse")) {
      me.onCollapseClick(event);
    } else if (target.closest(".b-splitter-button-expand")) {
      me.onExpandClick(event);
    } else if (((_a5 = me.collapseState) == null ? void 0 : _a5.status) !== "collapsed" && ((_b2 = me.collapseState) == null ? void 0 : _b2.status) !== "expanded" && ((_d = (_c2 = me.nextSplitter) == null ? void 0 : _c2.collapseState) == null ? void 0 : _d.status) !== "collapsed" && ((_f = (_e = me.previousSplitter) == null ? void 0 : _e.collapseState) == null ? void 0 : _f.status) !== "expanded") {
      me.startMove(event);
    }
  }
  startMove(event) {
    var _a5, _b2;
    event.preventDefault();
    if (event.touches) {
      event = event.touches[0];
    }
    const me = this, {
      element,
      nextNeighbor,
      previousNeighbor
    } = me, prev = previousNeighbor ? previousNeighbor.element : me.getSibling(false), next = nextNeighbor ? nextNeighbor.element : me.getSibling(), prevHasFlex = hasFlex(prev), nextHasFlex = hasFlex(next), flexed = [];
    (_a5 = me.mouseDetacher) == null ? void 0 : _a5.call(me);
    if (me.showButtons) {
      me.collapseState = null;
      me.setButtonVisibility();
    }
    for (const child of element.parentElement.children) {
      if (hasFlex(child) && child !== element && ((_b2 = Widget.fromElement(child, element.parentElement)) == null ? void 0 : _b2.type) !== "splitter") {
        flexed.push({
          element: child,
          width: child.offsetWidth,
          height: child.offsetHeight
        });
      }
    }
    me.context = {
      startX: event.pageX,
      startY: event.pageY,
      prevWidth: prev.offsetWidth,
      prevHeight: prev.offsetHeight,
      nextWidth: next.offsetWidth,
      nextHeight: next.offsetHeight,
      prevHasFlex,
      nextHasFlex,
      flexed,
      prev,
      next
    };
    const events = {
      element: document,
      pointermove: "onMouseMove",
      pointerup: "onMouseUp",
      thisObj: me
    };
    if (!BrowserHelper.supportsPointerEvents) {
      events.mousemove = events.touchmove = "onMouseMove";
      events.mouseup = events.touchend = "onMouseUp";
    }
    element.classList.add("b-moving");
    me.mouseDetacher = EventHelper.on(events);
    me.trigger("splitterMouseDown", { event });
  }
  onMouseEnter(event) {
    const me = this;
    me.syncState();
    me.trigger("splitterMouseEnter", { event });
    if (me.showButtons) {
      me.setSplitterButtonPosition();
      me.setButtonVisibility();
    }
    me.element.classList.add("b-hover");
  }
  onMouseLeave(event) {
    this.trigger("splitterMouseLeave", { event });
    this.element.classList.remove("b-hover");
  }
  onMouseMove(event) {
    const {
      context,
      nextWidget,
      previousWidget,
      vertical,
      rtl
    } = this, prevStyle = context.prev.style, nextStyle = context.next.style, deltaX = (event.pageX - context.startX) * (rtl ? -1 : 1), deltaY = event.pageY - context.startY;
    event.preventDefault();
    Object.assign(context, {
      deltaX,
      deltaY
    });
    if (!context.started) {
      context.started = true;
      this.trigger("dragStart", { context, event });
      context.flexed.forEach((flexed) => {
        flexed.element.style.flexGrow = vertical ? flexed.width : flexed.height;
        flexed.element.style.flexBasis = "0";
      });
    }
    if (vertical) {
      const newPrevWidth = context.prevWidth + deltaX, newNextWidth = context.nextWidth - deltaX;
      if (context.prevHasFlex) {
        prevStyle.flexGrow = newPrevWidth;
      } else if (previousWidget) {
        previousWidget.width = newPrevWidth;
      } else {
        prevStyle.width = `${newPrevWidth}px`;
      }
      if (context.nextHasFlex) {
        nextStyle.flexGrow = newNextWidth;
      } else if (nextWidget) {
        nextWidget.width = newNextWidth;
      } else {
        nextStyle.width = `${newNextWidth}px`;
      }
    } else {
      const newPrevHeight = context.prevHeight + deltaY, newNextHeight = context.nextHeight - deltaY;
      if (context.prevHasFlex) {
        prevStyle.flexGrow = newPrevHeight;
      } else if (previousWidget) {
        previousWidget.height = newPrevHeight;
      } else {
        prevStyle.height = `${newPrevHeight}px`;
      }
      if (context.nextHasFlex) {
        nextStyle.flexGrow = newNextHeight;
      } else if (nextWidget) {
        nextWidget.height = newNextHeight;
      } else {
        nextStyle.height = `${newNextHeight}px`;
      }
    }
    this.trigger("drag", { context, event });
  }
  onMouseUp(event) {
    var _a5;
    const me = this;
    (_a5 = me.mouseDetacher) == null ? void 0 : _a5.call(me);
    me.mouseDetacher = null;
    me.element.classList.remove("b-moving");
    if (me.context.started) {
      me.trigger("drop", { context: me.context, event });
    }
    me.context = null;
  }
  onScroll(context) {
    const { deltaY, shiftKey } = context, { vertical } = this;
    if (shiftKey && !vertical || !shiftKey && vertical) {
      this.setSplitterButtonPosition(deltaY);
    }
  }
  onCollapseClick(event) {
    var _a5, _b2, _c2, _d, _e;
    const me = this, {
      nextNeighbor,
      nextWidget,
      previousNeighbor,
      previousWidget,
      collapseState,
      showButtons,
      vertical
    } = me, prev = previousNeighbor ? previousNeighbor.element : me.getSibling(false), next = nextNeighbor ? nextNeighbor.element : me.getSibling(), prevHasFlex = hasFlex(prev), nextHasFlex = hasFlex(next);
    if (showButtons === "end" && !(previousWidget == null ? void 0 : previousWidget.collapsible) && (collapseState == null ? void 0 : collapseState.status) !== "expanded" || (collapseState == null ? void 0 : collapseState.status) === "collapsed") {
      return;
    }
    event.stopPropagation();
    let nextCollapseState = {
      status: "collapsed"
    };
    me.element.classList.toggle("b-show-buttons", !collapseState);
    if (previousWidget && !(collapseState == null ? void 0 : collapseState.status) && vertical && previousWidget.minWidth > 0) {
      nextCollapseState = {
        ...nextCollapseState,
        prevMinWidth: previousWidget.minWidth
      };
      previousWidget.minWidth = 0;
    } else if (previousWidget && !(collapseState == null ? void 0 : collapseState.status) && !vertical && previousWidget.minHeight > 0) {
      nextCollapseState = {
        ...nextCollapseState,
        prevMinHeight: previousWidget.minHeight
      };
      previousWidget.minHeight = 0;
    }
    if (nextWidget && (collapseState == null ? void 0 : collapseState.status) === "expanded" && ((collapseState == null ? void 0 : collapseState.nextMinWidth) || (collapseState == null ? void 0 : collapseState.nextMinHeight))) {
      if (vertical) {
        nextWidget.minWidth = collapseState.nextMinWidth;
      } else {
        nextWidget.minHeight = collapseState.nextMinHeight;
      }
    }
    if (!collapseState && (previousWidget == null ? void 0 : previousWidget.collapsible) && !(previousWidget == null ? void 0 : previousWidget.collapsed)) {
      if (previousWidget.collapsible.isPanelCollapser) {
        previousWidget.collapsible.collapse();
      } else {
        previousWidget.collapse();
      }
    } else if ((collapseState == null ? void 0 : collapseState.status) === "expanded" && (nextWidget == null ? void 0 : nextWidget.collapsible) && ((nextWidget == null ? void 0 : nextWidget.collapsed) || ((_a5 = nextWidget == null ? void 0 : nextWidget.collapsible) == null ? void 0 : _a5.collapsed))) {
      if (nextWidget.collapsible.isPanelCollapser) {
        nextWidget.collapsible.collapse(false);
      } else {
        nextWidget.expand();
      }
    } else {
      if ((collapseState == null ? void 0 : collapseState.status) === "expanded") {
        me.restoreOriginalState();
      } else {
        me.lockFlexedSiblings();
        nextCollapseState = {
          ...nextCollapseState,
          prevWidth: prev.offsetWidth,
          prevHeight: prev.offsetHeight,
          prevHasFlex,
          prevFlexGrow: prevHasFlex ? prev.style.flexGrow : null,
          nextWidth: next.offsetWidth,
          nextHeight: next.offsetHeight,
          nextHasFlex,
          nextFlexGrow: nextHasFlex ? next.style.flexGrow : null
        };
        if (me.vertical) {
          const newWidth = prev.offsetWidth + next.offsetWidth;
          if (prevHasFlex) {
            prev.style.flexGrow = 1;
          } else {
            prev.style.width = "0px";
          }
          if (((_c2 = (_b2 = me.nextSplitter) == null ? void 0 : _b2.collapseState) == null ? void 0 : _c2.status) !== "collapsed") {
            if (nextHasFlex) {
              next.style.flexGrow = newWidth;
            } else {
              next.style.width = `${newWidth}px`;
            }
          } else {
            if (nextHasFlex) {
              next.style.flexGrow = 1;
            } else {
              next.style.width = "0px";
            }
          }
        } else {
          const newHeight = prev.offsetHeight + next.offsetHeight;
          if (prevHasFlex) {
            prev.style.flexGrow = 1;
          } else {
            prev.style.height = "0px";
          }
          if (((_e = (_d = me.nextSplitter) == null ? void 0 : _d.collapseState) == null ? void 0 : _e.status) !== "collapsed") {
            if (nextHasFlex) {
              next.style.flexGrow = newHeight;
            } else {
              next.style.height = `${newHeight}px`;
            }
          } else {
            if (nextHasFlex) {
              next.style.flexGrow = 1;
            } else {
              next.style.height = "0px";
            }
          }
        }
      }
    }
    me.collapseState = collapseState ? null : nextCollapseState;
    me.element.classList.toggle("b-drag-disabled", !collapseState);
    me.setButtonVisibility();
  }
  onExpandClick(event) {
    var _a5, _b2, _c2, _d, _e;
    const me = this, {
      nextNeighbor,
      nextWidget,
      previousNeighbor,
      previousWidget,
      collapseState,
      showButtons,
      vertical
    } = me, prev = previousNeighbor ? previousNeighbor.element : me.getSibling(false), next = nextNeighbor ? nextNeighbor.element : me.getSibling(), prevHasFlex = hasFlex(prev), nextHasFlex = hasFlex(next);
    if (showButtons === "start" && !(nextWidget == null ? void 0 : nextWidget.collapsible) && (collapseState == null ? void 0 : collapseState.status) !== "collapsed" || (collapseState == null ? void 0 : collapseState.status) === "expanded") {
      return;
    }
    event.stopPropagation();
    let nextCollapseState = {
      status: "expanded"
    };
    me.element.classList.toggle("b-show-buttons", !collapseState);
    if (!(collapseState == null ? void 0 : collapseState.status) && vertical && nextWidget.minWidth > 0) {
      nextCollapseState = {
        ...nextCollapseState,
        nextMinWidth: nextWidget.minWidth
      };
      nextWidget.minWidth = 0;
    } else if (!(collapseState == null ? void 0 : collapseState.status) && !vertical && nextWidget.minHeight > 0) {
      nextCollapseState = {
        ...nextCollapseState,
        nextMinHeight: nextWidget.minHeight
      };
      nextWidget.minHeight = 0;
    }
    if (previousWidget && (collapseState == null ? void 0 : collapseState.status) === "collapsed" && ((collapseState == null ? void 0 : collapseState.prevMinWidth) || (collapseState == null ? void 0 : collapseState.prevMinHeight))) {
      if (vertical) {
        previousWidget.minWidth = collapseState.prevMinWidth;
      } else {
        previousWidget.minHeight = collapseState.prevMinHeight;
      }
    }
    if (!collapseState && (nextWidget == null ? void 0 : nextWidget.collapsible) && !(nextWidget == null ? void 0 : nextWidget.collapsed)) {
      if (nextWidget.collapsible.isPanelCollapser) {
        nextWidget.collapsible.collapse();
      } else {
        nextWidget.collapse();
      }
    } else if ((collapseState == null ? void 0 : collapseState.status) === "collapsed" && (previousWidget == null ? void 0 : previousWidget.collapsible) && ((previousWidget == null ? void 0 : previousWidget.collapsed) || ((_a5 = previousWidget == null ? void 0 : previousWidget.collapsible) == null ? void 0 : _a5.collapsed))) {
      if (previousWidget.collapsible.isPanelCollapser) {
        previousWidget.collapsible.collapse(false);
      } else {
        previousWidget.expand();
      }
    } else {
      if ((collapseState == null ? void 0 : collapseState.status) === "collapsed") {
        me.restoreOriginalState();
      } else {
        me.lockFlexedSiblings();
        nextCollapseState = {
          ...nextCollapseState,
          prevWidth: prev.offsetWidth,
          prevHeight: prev.offsetHeight,
          prevHasFlex,
          prevFlexGrow: prevHasFlex ? prev.style.flexGrow : null,
          nextWidth: next.offsetWidth,
          nextHeight: next.offsetHeight,
          nextHasFlex,
          nextFlexGrow: nextHasFlex ? next.style.flexGrow : null
        };
        if (me.vertical) {
          const newWidth = prev.offsetWidth + next.offsetWidth;
          if (((_c2 = (_b2 = me.previousSplitter) == null ? void 0 : _b2.collapseState) == null ? void 0 : _c2.status) !== "expanded") {
            if (prevHasFlex) {
              prev.style.flexGrow = newWidth;
            } else {
              prev.style.width = `${newWidth}px`;
            }
          } else {
            if (prevHasFlex) {
              prev.style.flexGrow = 1;
            } else {
              prev.style.width = "0px";
            }
          }
          if (nextHasFlex) {
            next.style.flexGrow = 1;
          } else {
            next.style.width = "0px";
          }
        } else {
          const newHeight = prev.offsetHeight + next.offsetHeight;
          if (((_e = (_d = me.previousSplitter) == null ? void 0 : _d.collapseState) == null ? void 0 : _e.status) !== "expanded") {
            if (prevHasFlex) {
              prev.style.flexGrow = newHeight;
            } else {
              prev.style.height = `${newHeight}px`;
            }
          } else {
            if (prevHasFlex) {
              prev.style.flexGrow = 1;
            } else {
              prev.style.height = "0px";
            }
          }
          if (nextHasFlex) {
            next.style.flexGrow = 1;
          } else {
            next.style.height = "0px";
          }
        }
      }
    }
    me.collapseState = collapseState ? null : nextCollapseState;
    me.element.classList.toggle("b-drag-disabled", !collapseState);
    me.setButtonVisibility();
  }
  //endregion
  render() {
    super.render(...arguments);
    this.syncState.now();
    if (this.vertical === null) {
      this.syncState();
    }
  }
};
//region Config
__publicField(Splitter, "$name", "Splitter");
__publicField(Splitter, "type", "splitter");
Splitter.initClass();
Splitter._$name = "Splitter";

// ../Core/lib/Core/widget/Tab.js
var Tab = class extends Button {
  static get configurable() {
    return {
      /**
       * This config is set to `true` when this tab represents the `activeTab` of a {@link Core.widget.TabPanel}.
       * It is managed by the tab panel and should not be set directly.
       * @config {Boolean} active
       * @internal
       * @default false
       */
      active: null,
      /**
       * This config is set to the ordinal position of this tab in the {@link Core.widget.TabPanel}.
       * It is managed by the tab panel and should not be set directly.
       * @internal
       * @config {Number} index
       */
      index: null,
      /**
       * This config is set to `true` when this tab represents the first tab of a {@link Core.widget.TabPanel}.
       * It is managed by the tab panel and should not be set directly.
       * @internal
       * @config {Boolean} isFirst
       */
      isFirst: null,
      /**
       * This config is set to `true` when this tab represents the last tab of a {@link Core.widget.TabPanel}.
       * It is managed by the tab panel and should not be set directly.
       * @internal
       * @config {Boolean} isLast
       */
      isLast: null,
      /**
       * The {@link Core.widget.Widget} in the {@link Core.widget.TabPanel} corresponding to this tab.
       * It is managed by the tab panel and should not be set directly.
       * @internal
       * @config {Core.widget.Widget} item
       */
      item: {
        value: null,
        $config: "nullify"
      },
      itemCls: null,
      /**
       * The tab panel that owns this tab.
       * @config {Core.widget.TabPanel} tabPanel
       * @internal
       */
      tabPanel: null,
      /**
       * The config property on this tab that will be set to the value of the {@link #config-titleSource} property
       * of this tab's {@link #config-item}.
       *
       * By default, the {@link #config-text} property of the tab is set to the {@link Core.widget.Widget#config-title}
       * property of its {@link #config-item}.
       * @config {String} titleProperty
       * @default
       */
      titleProperty: "text",
      /**
       * The config property on this tab's {@link #config-item} that is used to set the value of the
       * {@link #config-titleProperty} of this tab.
       *
       * By default, the {@link #config-text} property of the tab is set to the {@link Core.widget.Widget#config-title}
       * property of its {@link #config-item}.
       * @config {String} titleSource
       * @default
       */
      titleSource: "title",
      /**
       * Set to `true` to show a close icon to remove the tab from the owning TabPanel
       * @prp {Boolean}
       */
      closable: null,
      role: "tab"
    };
  }
  compose(domConfig) {
    const { active, cls, index, isFirst, isLast, closable } = this, setSize = this.owner.visibleChildCount;
    if (closable) {
      domConfig.children.closeIcon = {
        tag: "i",
        class: "b-tab-close b-icon b-icon-close"
      };
    }
    return {
      tabindex: 0,
      "aria-selected": active,
      "aria-setsize": setSize,
      "aria-posinset": index + 1,
      class: {
        "b-tabpanel-tab": 1,
        "b-active": active,
        "b-tab-first": isFirst,
        "b-tab-last": isLast,
        ...cls
        // cls is a DomClassList
      },
      dataset: {
        index
      }
    };
  }
  //endregion
  updateIndex(index) {
    this.isFirst = !index;
  }
  updateItem(item, was) {
    var _a5, _b2;
    const me = this;
    if ((was == null ? void 0 : was.tab) === me) {
      was.tab = null;
    }
    if (item) {
      item.tab = me;
      me[me.titleProperty] = item[me.titleSource];
      me.itemCls = item.cls;
      me.ariaElement.setAttribute("aria-controls", item.id);
      item.role = "tabpanel";
    }
    (_a5 = me.itemChangeDetacher) == null ? void 0 : _a5.call(me);
    me.itemChangeDetacher = item && FunctionHelper.after(item, "onConfigChange", "onItemConfigChange", me, {
      return: false
    });
    (_b2 = me.itemHideDetacher) == null ? void 0 : _b2.call(me);
    me.itemHideDetacher = item == null ? void 0 : item.ion({
      beforeChangeHidden: "onItemBeforeChangeHidden",
      beforeHide: "onItemBeforeHide",
      beforeUpdateDisabled: "onItemBeforeUpdateDisabled",
      thisObj: me,
      prio: 1e3
      // We must know before the layout intercepts and activates a sibling
    });
    me.syncMinMax();
  }
  updateItemCls(cls, was) {
    const { element } = this, classList = element && DomClassList.from(
      element == null ? void 0 : element.classList,
      /* returnEmpty= */
      true
    );
    if (element) {
      classList.remove(was).add(cls);
      element.className = classList.value;
    }
  }
  updateRotate(rotate, was) {
    if (!rotate !== !was) {
      this.syncMinMax();
    }
  }
  syncMinMax() {
    const me = this, { rotate, tabPanel } = me;
    let { _minWidth: minWidth, _minHeight: minHeight, _maxWidth: maxWidth, _maxHeight: maxHeight } = me;
    if (tabPanel) {
      const { tabMinWidth, tabMaxWidth } = tabPanel;
      if (tabMinWidth != null) {
        if (rotate) {
          if (minWidth === tabMinWidth) {
            minWidth = null;
          }
          minHeight = tabMinWidth;
        } else {
          if (minHeight === tabMinWidth) {
            minHeight = null;
          }
          minWidth = tabMinWidth;
        }
      }
      if (tabMaxWidth != null) {
        if (rotate) {
          if (maxWidth === tabMaxWidth) {
            maxWidth = null;
          }
          maxHeight = tabMaxWidth;
        } else {
          if (maxHeight === tabMaxWidth) {
            maxHeight = null;
          }
          maxWidth = tabMaxWidth;
        }
      }
      me.minWidth = minWidth;
      me.minHeight = minHeight;
      me.maxWidth = maxWidth;
      me.maxHeight = maxHeight;
    }
  }
  onItemBeforeChangeHidden({ source: hidingChild, hidden }) {
    if (!hidingChild.$isDeactivating && !hidingChild.$isActivating) {
      const { tabPanel } = this;
      this.hidden = hidden;
      if (hidden && hidingChild === tabPanel.activeItem) {
        tabPanel.activateAvailableTab(hidingChild);
      }
    }
  }
  onItemBeforeHide() {
    if (!this.item.$isDeactivating) {
      this.hide();
    }
  }
  onItemBeforeUpdateDisabled({ source: disablingChild, disabled }) {
    const { tabPanel } = this;
    this.disabled = disabled;
    if (disablingChild === tabPanel.activeItem) {
      tabPanel.activateAvailableTab(disablingChild);
    }
  }
  onItemConfigChange({ name, value: value2 }) {
    if (name === this.titleSource) {
      this[this.titleProperty] = value2;
    }
  }
  // Prevent only tab being removed
  get closable() {
    return this._closable && !(this.isFirst && this.isLast);
  }
};
//region Config
__publicField(Tab, "$name", "Tab");
__publicField(Tab, "type", "tab");
Tab.initClass();
Tab._$name = "Tab";

// ../Core/lib/Core/widget/TabBar.js
var isTab = (t) => t.isTab;
var TabBar = class extends Toolbar {
  get firstTab() {
    return this.tabAt(0);
  }
  get lastTab() {
    return this.tabAt(-1);
  }
  get tabCount() {
    return this._items.countOf(isTab);
  }
  get tabs() {
    return ArrayHelper.from(this._items, isTab);
  }
  compose() {
    return {
      children: {
        toolbarContent: {
          class: {
            "b-tabpanel-tabs": 1
          }
        }
      }
    };
  }
  indexOfTab(tab) {
    return this._items.indexOf(tab, isTab);
  }
  onChildAdd(child) {
    super.onChildAdd(child);
    if (child.index == null) {
      this.syncTabs();
    }
  }
  onChildRemove(child) {
    super.onChildRemove(child);
    this.syncTabs();
  }
  onFocusIn({ _target }) {
    const { activeIndex } = this.owner;
    if ((_target == null ? void 0 : _target.matches(".b-tab:focus-visible")) && !isNaN(activeIndex)) {
      this.tabs[activeIndex].focus();
    }
  }
  syncTabs() {
    const { tabs } = this;
    for (let i = 0, n = tabs.length; i < n; ++i) {
      tabs[i].index = i;
      tabs[i].isFirst = !i;
      tabs[i].isLast = i === n - 1;
    }
  }
  tabAt(index) {
    return this._items.find(isTab, index) || null;
  }
  //endregion
  // region drag-drop
  onChildMove() {
    this.syncTabs();
  }
  /**
   * Handle drop
   * @private
   */
  onDrop({ context }) {
    const { item, valid, oldIndex } = context, { owner } = this;
    super.onDrop(...arguments);
    if (valid && owner) {
      const { owner: owner2 } = this, wasActive = item.active, index = Array.from(item.element.parentElement.children).indexOf(item.element);
      owner2.insert(owner2.items[oldIndex], index);
      if (wasActive) {
        owner2.layout.setActiveItem(index, oldIndex, { animation: false, silent: true });
        owner2.activeTab = item.item;
      }
    }
  }
  //endregion
};
__publicField(TabBar, "$name", "TabBar");
__publicField(TabBar, "type", "tabbar");
__publicField(TabBar, "configurable", {
  defaultType: "tab",
  overflow: "scroll",
  role: "tablist",
  ignoreParentReadOnly: true
});
TabBar.initClass();
TabBar._$name = "TabBar";

// ../Core/lib/Core/widget/layout/Card.js
var animationClasses = [
  "b-slide-out-left",
  "b-slide-out-right",
  "b-slide-in-left",
  "b-slide-in-right"
];
var focusOptions = { preventScroll: true };
var Card = class extends Layout {
  onChildAdd(item) {
    super.onChildAdd(item);
    const me = this, {
      activeItem,
      owner
    } = me, activeIndex = owner.activeIndex != null ? owner.activeIndex : me.activeIndex || 0, itemIndex = owner.items.indexOf(item), isActive2 = activeItem != null ? item === activeItem : itemIndex === activeIndex;
    item.ion({
      beforeHide: "onBeforeChildHide",
      beforeShow: "onBeforeChildShow",
      // We need to be the first to know about the card being shown so that we can update our active item.
      show: {
        fn: "onChildShow",
        prio: 99999
      },
      thisObj: me
    });
    if (isActive2) {
      me._activeIndex = itemIndex;
      me._activeItem = item;
      item.show();
    } else {
      item.$isDeactivating = true;
      item.hide();
      item.$isDeactivating = false;
    }
  }
  onChildRemove(item) {
    super.onChildRemove(item);
    const me = this;
    if (me._activeItem === item) {
      me.activateSiblingOf(item);
    }
    me._activeIndex = me.owner.items.indexOf(me._activeItem);
    item.un({
      beforeHide: "onBeforeChildHide",
      beforeShow: "onBeforeChildShow",
      show: "onChildShow",
      thisObj: me
    });
  }
  /**
   * Detect external code showing a child. We veto that show and activate it through the API.
   * @internal
   */
  onBeforeChildShow({ source: showingChild }) {
    if (!this.owner.isConfiguring && !showingChild.$isActivating) {
      this.activeItem = showingChild;
      return false;
    }
  }
  // Ensure that the high priority show listener updates our active item immediately the new card is shown.
  onChildShow({ source }) {
    this._activeItem = source;
  }
  /**
   * Detect external code hiding a child. We veto that show and activate an immediate sibling through the API.
   * @internal
   */
  onBeforeChildHide({ source: hidingChild }) {
    if (!this.owner.isConfiguring && !hidingChild.$isDeactivating) {
      this.activateSiblingOf(hidingChild);
      return false;
    }
  }
  activateSiblingOf(item) {
    const { owner } = this, items2 = owner.items.slice(), removeAt = items2.indexOf(item);
    items2.splice(removeAt, 1);
    this.activeIndex = Math.min(removeAt, items2.length - 1);
  }
  /**
   * Get/set active item, using index or the Widget to activate
   * @param {Core.widget.Widget|Number} activeIndex
   * @param {Number} [prevActiveIndex]
   * @param {Object} [options]
   * @param {Boolean} [options.animation] Pass `false` to disable animation
   * @param {Boolean} [options.silent] Pass `true` to not fire transition events
   * @returns {Object} An object describing the card change containing the following properties:
   *  - `prevActiveIndex` The previously active index.
   *  - `prevActiveItem ` The previously active child item.
   *  - `activeIndex    ` The newly active index.
   *  - `activeItem     ` The newly active child item.
   *  - `promise        ` A promise which completes when the slide-in animation finishes and the child item contains
   * focus if it is focusable.
   * @internal
   */
  setActiveItem(activeIndex, prevActiveIndex = this.activeIndex, options) {
    var _a5;
    const me = this, { owner } = me, { items: items2 } = owner, widgetPassed = activeIndex instanceof Widget, prevActiveItem = items2[prevActiveIndex], newActiveItem = owner.items[activeIndex = widgetPassed ? items2.indexOf(activeIndex) : parseInt(activeIndex, 10)], animation = (options == null ? void 0 : options.animation) !== false, chatty = !(options == null ? void 0 : options.silent), event = {
      prevActiveIndex,
      prevActiveItem,
      containedFocus: prevActiveItem == null ? void 0 : prevActiveItem.element.matches(":focus-within"),
      ownerContainedFocus: owner.element.matches(":focus-within")
    };
    if (newActiveItem && !newActiveItem.$isActivating && newActiveItem !== prevActiveItem) {
      const prevItemElement = prevActiveItem && prevActiveItem.element, newActiveElement = newActiveItem && newActiveItem.element;
      if (me.animateDetacher) {
        const activeCardChange = me.animateDetacher.event;
        if (activeCardChange.activeItem === newActiveItem) {
          return activeCardChange.promise;
        }
        me.animateDetacher();
        activeCardChange.prevActiveItem.element.classList.remove(...animationClasses);
        activeCardChange.activeItem.element.classList.remove(...animationClasses);
        me.animateDetacher = null;
      }
      event.activeIndex = activeIndex;
      event.activeItem = newActiveItem;
      if (chatty && owner.trigger("beforeActiveItemChange", event) === false) {
        return null;
      }
      const reset = me._activeIndex !== event.activeIndex;
      if (reset) {
        me._activeIndex = event.activeIndex;
      }
      chatty && ((_a5 = owner.onBeginActiveItemChange) == null ? void 0 : _a5.call(owner, event));
      if (reset) {
        me._activeIndex = event.prevActiveIndex;
      }
      if (animation && prevItemElement && owner.isVisible && me.animateCardChange) {
        event.promise = me.cardChangeAnimation = new Promise((resolve, reject) => {
          const wasMonitoringSize = prevActiveItem.monitorResize;
          prevActiveItem.monitorResize = false;
          me.contentElement.style.overflowX = "hidden";
          prevActiveItem._hidden = true;
          newActiveItem.$isActivating = true;
          newActiveItem.show();
          if (owner.element.matches(":focus-within")) {
            newActiveItem.focus(focusOptions);
          }
          newActiveItem.$isActivating = false;
          prevItemElement.classList.add(activeIndex > prevActiveIndex ? "b-slide-out-left" : "b-slide-out-right");
          newActiveElement.classList.add(activeIndex < prevActiveIndex ? "b-slide-in-left" : "b-slide-in-right");
          owner.isAnimating = true;
          me.animateDetacher = EventHelper.onTransitionEnd({
            mode: "animation",
            element: newActiveElement,
            // onTransitionEnd protects us from being called
            // after the thisObj is destroyed.
            thisObj: prevActiveItem,
            handler() {
              owner.isAnimating = me.cardChangeAnimation = false;
              if (!me.animateDetacher) {
                me.setActiveItem(activeIndex, prevActiveIndex, options);
                return;
              }
              me.animateDetacher = null;
              newActiveElement.classList.remove(...animationClasses);
              if (prevItemElement) {
                prevItemElement.classList.remove(...animationClasses);
                prevActiveItem.$isDeactivating = true;
                prevActiveItem._hidden = false;
                prevActiveItem.hide();
                prevActiveItem.monitorResize = wasMonitoringSize;
                prevActiveItem.$isDeactivating = false;
              }
              me.contentElement.style.overflowX = "";
              me.onActiveItemChange(event, resolve, !chatty);
            }
          });
          me.animateDetacher.reject = reject;
          me.animateDetacher.event = event;
        });
      } else {
        newActiveItem.$isActivating = true;
        newActiveItem.show();
        if (owner.element.matches(":focus-within")) {
          newActiveItem.focus(focusOptions);
        }
        newActiveItem.$isActivating = false;
        if (prevActiveItem) {
          prevActiveItem.$isDeactivating = true;
          prevActiveItem.hide();
          prevActiveItem.$isDeactivating = false;
        }
        me.onActiveItemChange(event, null, !chatty);
      }
    }
    return event;
  }
  onActiveItemChange(event, resolve, silent) {
    const me = this, { owner } = me, { activeItem } = event;
    me._activeItem = activeItem;
    me._activeIndex = event.activeIndex;
    !silent && owner.trigger("activeItemChange", event);
    event.ownerContainedFocus && !activeItem.element.matches(":focus-within") && activeItem.focus(focusOptions);
    resolve == null ? void 0 : resolve(event);
  }
  renderChildren() {
    const { owner } = this;
    owner.contentElement.classList.toggle(this.hideChildHeaderCls, owner.suppressChildHeaders);
    super.renderChildren();
  }
  changeActiveIndex(activeIndex) {
    const { owner } = this;
    return owner.isConfiguring && !owner._items ? activeIndex : Math.min(activeIndex, owner.items.length - 1);
  }
  updateActiveIndex(activeIndex, oldActiveIndex) {
    if (!this.owner.isConfiguring) {
      this.setActiveItem(activeIndex, oldActiveIndex);
    }
  }
  updateActiveItem(activeItem) {
    if (!this.owner.isConfiguring) {
      this.setActiveItem(activeItem, this.activeIndex);
    }
  }
  /**
   * If the layout is set to {@link #config-animateCardChange}, then this property
   * will be `true` during the animated card change.
   * @property {Boolean}
   * @readonly
   */
  get isChangingCard() {
    return Boolean(this.animateDetacher);
  }
  insertChild(toAdd) {
    const me = this, isMove = me.owner.items.includes(toAdd);
    if (isMove) {
      const currentActive = me.activeItem, wasActive = toAdd === currentActive;
      if (wasActive) {
        me._activeItem = null;
      }
      const result = super.insertChild(...arguments);
      if (!wasActive) {
        me._activeIndex = me.owner.items.indexOf(currentActive);
      }
      return result;
    }
    return super.insertChild(...arguments);
  }
};
__publicField(Card, "$name", "Card");
__publicField(Card, "type", "card");
__publicField(Card, "configurable", {
  containerCls: "b-card-container",
  itemCls: "b-card-item",
  hideChildHeaderCls: "b-hide-child-headers",
  /**
   * Specifies whether to slide tabs in and out of visibility.
   * @config {Boolean}
   * @default
   */
  animateCardChange: true,
  /**
   * The active child item.
   * @config {Core.widget.Widget}
   */
  activeItem: null,
  /**
   * The active child index.
   * @config {Number}
   */
  activeIndex: null
});
Card.initClass();
Card._$name = "Card";

// ../Core/lib/Core/widget/TabPanel.js
var isMaximized = (w) => w.maximized;
var TabPanel = class extends Panel {
  //endregion
  //region Init
  /**
   * The active tab index. Setting must be done through {@link #property-activeTab}
   * @property {Number}
   * @readonly
   */
  get activeIndex() {
    return this.layout.activeIndex;
  }
  /**
   * The active child widget. Setting must be done through {@link #property-activeTab}
   * @property {Core.widget.Widget}
   * @readonly
   */
  get activeItem() {
    return this.layout.activeItem;
  }
  get activeTabItemIndex() {
    var _a5;
    const { activeTab, items: items2, tabBar } = this;
    return items2.indexOf((_a5 = tabBar.tabs[activeTab]) == null ? void 0 : _a5.item);
  }
  get bodyConfig() {
    return ObjectHelper.merge({
      className: {
        "b-tabpanel-body": 1
      }
    }, super.bodyConfig);
  }
  get focusElement() {
    var _a5;
    const activeTab = this.items[this.activeTab || 0];
    return (activeTab == null ? void 0 : activeTab.focusElement) || ((_a5 = activeTab == null ? void 0 : activeTab.tab) == null ? void 0 : _a5.focusElement);
  }
  get tabPanelBody() {
    return this.bodyElement;
  }
  finalizeInit() {
    super.finalizeInit();
    const me = this, { activeTab, layout } = me, { activeIndex } = layout, { tabs } = me.tabBar, activeTabItemIndex = activeTab >= 0 && activeTab < tabs.length && me.items.indexOf(tabs[activeTab].item);
    if (tabs.length > 0 && (activeTabItemIndex === false || activeTabItemIndex < 0)) {
      throw new Error(`Invalid activeTab ${activeTab} (${tabs.length} tabs)`);
    }
    if (activeTabItemIndex !== activeIndex) {
      layout.setActiveItem(activeTabItemIndex, activeIndex, {
        animation: false,
        silent: true
      });
    }
    layout.animateCardChange = me.animateTabChange;
  }
  onChildAdd(child) {
    const me = this;
    super.onChildAdd(child);
    if (!me.initialItems) {
      const { tabBar } = me, config = me.makeTabConfig(child), firstTab = config && (tabBar == null ? void 0 : tabBar.firstTab), tabBarItems = firstTab && tabBar._items, tabItems = firstTab && ArrayHelper.from(me._items, (it) => it.tab || it === child), index = firstTab ? tabItems.indexOf(child) + tabBarItems.indexOf(firstTab) : 0;
      if (config && tabBar) {
        if (firstTab && child.weight == null && index < tabBarItems.count - 1) {
          tabBar.insert(config, index);
        } else {
          tabBar.add(config);
        }
      }
    }
  }
  onChildRemove(child) {
    const me = this, { tab } = child, { items: items2 } = me;
    if (tab) {
      me.tabBar.remove(tab);
      tab.destroy();
    }
    if (child === me.activeItem) {
      me._activeTab = null;
      if (items2.length) {
        me.activeTab = items2[Math.min(me.activeIndex, items2.length - 1)];
      }
    }
    super.onChildRemove(child);
    if (child.tabBarItems) {
      me.tabBar.remove(child.tabBarItems);
    }
  }
  //endregion
  //region Tabs
  isDisabledOrHiddenTab(tabIndex) {
    const { tabs } = this.tabBar, tab = tabs == null ? void 0 : tabs[tabIndex];
    return tab && (tab.disabled || tab.hidden);
  }
  findAvailableTab(item, delta = 1) {
    const { tabs } = this.tabBar, tabCount = tabs.length, itemIndex = Math.max(0, tabs.indexOf(item.tab));
    if (itemIndex) {
      delta = -delta;
    }
    let activeTab;
    for (let n = 1; n <= tabCount; ++n) {
      activeTab = (itemIndex + (delta < 0 ? tabCount : 0) + n * delta) % tabCount;
      if (!this.isDisabledOrHiddenTab(activeTab)) {
        break;
      }
    }
    return activeTab;
  }
  activateAvailableTab(item, delta = 1) {
    this.activeTab = this.findAvailableTab(item, delta);
  }
  changeActiveTab(activeTab, oldActiveTab) {
    const me = this, {
      tabBar,
      layout
    } = me, { tabCount } = tabBar;
    if (activeTab.isWidget || ObjectHelper.isObject(activeTab)) {
      if (me.items.indexOf(activeTab) === -1) {
        activeTab = me.add(activeTab);
      }
      activeTab = tabBar.indexOfTab(activeTab.tab);
    } else {
      activeTab = parseInt(activeTab, 10);
    }
    if (!me.initialItems && tabCount > 0 && (activeTab < -1 || activeTab >= tabCount)) {
      throw new Error(`Invalid activeTab ${activeTab} (${tabCount} tabs)`);
    }
    if (me.isDisabledOrHiddenTab(activeTab)) {
      activeTab = me.findAvailableTab(activeTab);
    }
    if (layout.animateCardChange && layout.cardChangeAnimation) {
      layout.cardChangeAnimation.then((cardChange) => {
        if ((cardChange == null ? void 0 : cardChange.activeIndex) !== activeTab) {
          me._activeTab = activeTab;
          me.updateActiveTab(activeTab, oldActiveTab);
        }
      });
    } else {
      return activeTab;
    }
  }
  async updateActiveTab(activeTab, was) {
    var _a5;
    const me = this;
    if (!me.initialItems) {
      const { activeTabItemIndex, layout } = me;
      if (activeTabItemIndex > -1) {
        const newActiveItem = me.items[activeTabItemIndex];
        if (layout.activeItem !== newActiveItem) {
          if (layout.animateCardChange) {
            await me.tabSelectionPromise;
          }
          me.tabSelectionPromise = (_a5 = layout.setActiveItem(newActiveItem)) == null ? void 0 : _a5.promise;
        }
      }
      me.tabBar.remove(me.tabBar.items.filter((item) => !item.isTab && item.trueOwner));
    }
    const activeItem = me.items[activeTab];
    if (activeItem == null ? void 0 : activeItem.tabBarItems) {
      activeItem.tabBarItems = me.tabBar.add(activeItem.tabBarItems);
      activeItem.tabBarItems.forEach((item) => item.trueOwner = activeItem);
    }
  }
  changeTabBar(bar) {
    this.getConfig("strips");
    this.strips = {
      tabBar: bar
    };
    return this.strips.tabBar;
  }
  makeTabConfig(item) {
    const { tab } = item, config = {
      item,
      type: "tab",
      active: !item.hidden,
      tabPanel: this,
      disabled: Boolean(item.disabled),
      hidden: item.initialConfig.hidden,
      weight: item.weight || 0,
      internalListeners: {
        click: "onTabClick",
        thisObj: this
      },
      localizableProperties: {
        // our tabs copy their text from the item's title and so are not directly localized
        text: false
      }
    };
    if (tab === false) {
      return null;
    }
    return ObjectHelper.isObject(tab) ? Tab.mergeConfigs(config, tab) : config;
  }
  updateAnimateTabChange(animateTabChange) {
    if (!this.isConfiguring) {
      this.layout.animateCardChange = animateTabChange;
    }
  }
  updateItems(items2, was) {
    const me = this, { activeTab, initialItems } = me;
    let index = 0, tabs;
    super.updateItems(items2, was);
    if (initialItems) {
      tabs = Array.from(items2, (it) => me.makeTabConfig(it)).filter((it) => {
        if (it) {
          it.index = index++;
          return true;
        }
      });
      if (index) {
        tabs[activeTab].active = true;
        me.tabBar.add(tabs);
        me.activeTab = activeTab;
      }
    }
  }
  updateTabMinWidth(tabMinWidth) {
    var _a5;
    (_a5 = this.tabBar) == null ? void 0 : _a5.items.forEach((tab) => {
      if (tab.isTab) {
        tab.minWidth = tabMinWidth;
      }
    });
  }
  updateTabMaxWidth(tabMaxWidth) {
    var _a5;
    (_a5 = this.tabBar) == null ? void 0 : _a5.items.forEach((tab) => {
      if (tab.isTab) {
        tab.maxWidth = tabMaxWidth;
      }
    });
  }
  //endregion
  //region Auto height
  updateAutoHeight(autoHeight) {
    this.detachListeners("themeAutoHeight");
    autoHeight && GlobalEvents_default.ion({
      name: "themeAutoHeight",
      theme: "internalOnThemeChange",
      thisObj: this
    });
  }
  applyAutoHeight() {
    const me = this, { layout, activeTab, element } = me, { animateCardChange } = layout;
    layout.animateCardChange = false;
    me.flex = "0 0 0%";
    if (!me.up(isMaximized)) {
      const maxContentHeight = me.height = Math.max(...me.items.map((tab) => {
        me.activeTab = tab;
        return element.clientHeight;
      })) + 1;
      me.flex = `1 1 ${maxContentHeight}px`;
    }
    me.activeTab = activeTab;
    layout.animateCardChange = animateCardChange;
  }
  internalOnThemeChange() {
    if (this.isVisible) {
      this.applyAutoHeight();
    }
  }
  //endregion
  //region Events
  // Called after beforeActiveItemChange has fired and not been vetoed before animation and activeItemChange
  onBeginActiveItemChange(activeItemChangeEvent) {
    const tabs = this.tabBar.tabs, { activeItem, prevActiveItem } = activeItemChangeEvent;
    this.activeTab = tabs.indexOf(activeItem == null ? void 0 : activeItem.tab);
    if (prevActiveItem == null ? void 0 : prevActiveItem.tab) {
      prevActiveItem.tab.active = false;
    }
    if (activeItem == null ? void 0 : activeItem.tab) {
      activeItem.tab.active = true;
      activeItem.tab.show();
    }
  }
  // Auto called because Card layout triggers the beforeActiveItemChange on its owner
  onBeforeActiveItemChange(activeItemChangeEvent) {
    return this.trigger("beforeTabChange", activeItemChangeEvent);
  }
  // Auto called because Card layout triggers the activeItemChange on its owner
  onActiveItemChange(activeItemChangeEvent) {
    this.trigger("tabChange", activeItemChangeEvent);
  }
  onTabClick(event) {
    const { item } = event.source;
    if (item.tab.closable && event.event.target.classList.contains("b-tab-close")) {
      this.remove(item);
    } else {
      this.activeTab = item;
    }
  }
  onInternalPaint() {
    super.onInternalPaint(...arguments);
    if (this.autoHeight) {
      this.applyAutoHeight();
    }
  }
  onValidityChange({ source, valid }) {
    const owningTab = source.closest((widget) => widget.element.classList.contains("b-tabpanel-item"));
    if (owningTab) {
      const { tab } = owningTab;
      tab.element.classList.toggle("b-invalid", !valid);
      tab.ariaLabel = valid ? "" : this.L("L{TabPanel.containsInvalidFields}");
    }
  }
  //endregion
};
//region Config
__publicField(TabPanel, "$name", "TabPanel");
__publicField(TabPanel, "type", "tabpanel");
__publicField(TabPanel, "alias", "tabs");
__publicField(TabPanel, "configurable", {
  /**
   * The index of the initially active tab.
   * @member {Number} activeTab
   * @accepts {Number|Core.widget.Widget}
   */
  /**
   * The index of the initially active tab.
   * @config {Number}
   * @default
   */
  activeTab: 0,
  /**
   * Specifies whether to slide tabs in and out of visibility.
   * @config {Boolean}
   * @default
   */
  animateTabChange: true,
  /**
   * Set the height of all tabs to match the tab with the highest content.
   * @config {Boolean}
   * @default
   */
  autoHeight: false,
  defaultType: "container",
  focusable: false,
  itemCls: "b-tabpanel-item",
  layout: {
    type: "card"
  },
  // Prevent child panels from displaying a header unless explicitly configured with one
  suppressChildHeaders: true,
  /**
   * Additional configuration for the tab bar.
   *
   * Use this to add more non-tab items to the bar:
   *
   * ```javascript
   * new TabPanel({
   *    tabBar : {
   *       items : {
   *          spacer : '->', // Spacer which moves next item to the right
   *          button : { type : 'button', text : 'MyButton' }
   *       }
   *    }
   * });
   * ```
   *
   * @config {TabBarConfig}
   */
  tabBar: {
    type: "tabbar",
    weight: -2e3
  },
  /**
   * Min width of a tab title. 0 means no minimum width. This is default.
   * @config {Number}
   * @default
   */
  tabMinWidth: null,
  /**
   * Max width of a tab title. 0 means no maximum width. This is default.
   * @config {Number}
   * @default
   */
  tabMaxWidth: null
});
TabPanel.initClass();
TabPanel._$name = "TabPanel";

// ../Core/lib/Core/widget/TextAreaField.js
var TextAreaField = class extends Field {
  static get configurable() {
    return {
      /**
       * The resize style to apply to the `<textarea>` element.
       * @config {'none'|'both'|'horizontal'|'vertical'}
       * @default
       */
      resize: "none",
      inputAttributes: {
        tag: "textarea"
      }
    };
  }
  updateResize(resize) {
    this.input.style.resize = resize;
  }
};
__publicField(TextAreaField, "$name", "TextAreaField");
__publicField(TextAreaField, "type", "textareafield");
__publicField(TextAreaField, "alias", "textarea");
TextAreaField.initClass();
TextAreaField._$name = "TextAreaField";

// ../Core/lib/Core/widget/TextAreaPickerField.js
var _TextAreaPickerField = class _TextAreaPickerField extends PickerField {
  get inputElement() {
    const result = super.inputElement;
    result.readOnly = "readonly";
    result.reference = "displayElement";
    this.ariaElement = "displayElement";
    return result;
  }
  get nonEditableClickTarget() {
    return this.displayElement;
  }
  get focusElement() {
    var _a5;
    return ((_a5 = this._picker) == null ? void 0 : _a5.isVisible) ? this.input : this.displayElement;
  }
  get needsInputSync() {
    var _a5;
    return this.displayElement[this.inputValueAttr] !== String((_a5 = this.inputValue) != null ? _a5 : "");
  }
  showPicker() {
    this.picker.width = this.pickerWidth || this.pickerAlignElement.offsetWidth;
    super.showPicker(true);
  }
  focusPicker() {
    this.input.focus();
  }
  onPickerKeyDown(keyEvent) {
    const me = this, realInput = me.input;
    switch (keyEvent.key.trim() || keyEvent.code) {
      case "Escape":
        me.picker.hide();
        return;
      case "Enter":
        if (keyEvent.ctrlKey) {
          me.syncInputFieldValue();
          me.picker.hide();
        }
        break;
    }
    me.input = me.displayElement;
    const result = super.onPickerKeyDown(keyEvent);
    me.input = realInput;
    return result;
  }
  syncInputFieldValue(skipHighlight) {
    if (this.displayElement) {
      this.displayElement.value = this.inputValue;
    }
    super.syncInputFieldValue(skipHighlight);
  }
  changeValue(value2) {
    return value2 == null ? "" : value2;
  }
  changePicker(picker, oldPicker) {
    var _a5;
    const me = this, pickerWidth = me.pickerWidth || (picker == null ? void 0 : picker.width);
    picker = _TextAreaPickerField.reconfigure(oldPicker, picker ? Objects.merge({
      owner: me,
      forElement: me.pickerAlignElement,
      align: {
        matchSize: pickerWidth == null,
        anchor: me.overlayAnchor,
        target: me.pickerAlignElement
      },
      id: me.id + "-input",
      style: {
        resize: me.resize
      },
      html: (_a5 = me.value) != null ? _a5 : ""
    }, picker) : null, me);
    if (picker) {
      const input = me.input = picker.element;
      me.inputListenerRemover = EventHelper.on({
        element: input,
        thisObj: me,
        focus: "internalOnInputFocus",
        change: "internalOnChange",
        input: "internalOnInput",
        keydown: "internalOnKeyEvent",
        keypress: "internalOnKeyEvent",
        keyup: "internalOnKeyEvent"
      });
    }
    return picker;
  }
};
__publicField(_TextAreaPickerField, "$name", "TextAreaPickerField");
__publicField(_TextAreaPickerField, "type", "textareapickerfield");
__publicField(_TextAreaPickerField, "configurable", {
  /**
   * @hideconfigs editable
   */
  picker: {
    type: "widget",
    tag: "textarea",
    cls: "b-textareapickerfield-picker",
    scrollAction: "realign",
    align: {
      align: "t-b",
      axisLock: true
    },
    autoShow: false
  },
  triggers: {
    expand: {
      cls: "b-icon-picker",
      handler: "onTriggerClick"
    }
  },
  /**
   * The resize style to apply to the `<textarea>` element.
   * @config {'none'|'both'|'horizontal'|'vertical'}
   * @default
   */
  resize: "none",
  inputType: null,
  // Private at this level. The "display" element is always readonly.
  // editing takes place in the picker.
  editable: false
});
var TextAreaPickerField = _TextAreaPickerField;
TextAreaPickerField.initClass();
TextAreaPickerField._$name = "TextAreaPickerField";

// ../Core/lib/Core/widget/base/UndoRedoBase.js
var UndoRedoBase = class extends Container {
  static get configurable() {
    return {
      // Documented on subclasses
      project: null,
      stm: null,
      /**
       * Configure as `true` to show "Undo" and "Redo" as button texts. The buttons always have a tooltip
       * as a hint to the user as to their purpose.
       * @config {Boolean}
       */
      text: null,
      /**
       * Button color for the undo and redo buttons. See {@link Core.widget.Button#config-color}.
       * @config {String}
       */
      color: null,
      /**
       * Configure as `true` to show "0" badge on the undo and redo buttons when they have no actions
       * left to perform. By default when there are no actions, no badge is displayed.
       * @config {Boolean}
       */
      showZeroActionBadge: null,
      cls: "b-undo-controls b-toolbar",
      layoutStyle: {
        alignItems: "stretch",
        flexFlow: "row nowrap",
        overflow: "visible"
      },
      items: {
        undoBtn: {
          type: "button",
          icon: "b-icon-undo",
          tooltip: "L{UndoRedo.UndoLastAction}",
          onAction: "up.onUndo"
          // 'up.' means method is on a parent Widget.
        },
        transactionsCombo: {
          type: "combo",
          valueField: "idx",
          editable: false,
          store: {},
          emptyText: "L{UndoRedo.NoActions}",
          onAction: "up.onTransactionSelected",
          displayValueRenderer: "up.transactionsDisplayValueRenderer"
        },
        redoBtn: {
          type: "button",
          icon: "b-icon-redo",
          tooltip: "L{UndoRedo.RedoLastAction}",
          onAction: "up.onRedo"
        }
      },
      // This is treated as atomic by the Toolbar's menu overflow processing.
      overflowable: true
    };
  }
  afterConstruct() {
    this.updateUndoRedoControls();
  }
  updateStm(stm) {
    this.detachListeners("undoredo");
    stm == null ? void 0 : stm.ion({
      name: "undoredo",
      recordingstop: "updateUndoRedoControls",
      restoringstop: "updateUndoRedoControls",
      queueReset: "updateUndoRedoControls",
      disabled: "updateUndoRedoControls",
      thisObj: this
    });
  }
  changeItems(items2) {
    const { undoBtn, redoBtn } = items2;
    if (this.color) {
      undoBtn && (undoBtn.color = this.color);
      redoBtn && (redoBtn.color = this.color);
    }
    if (this.text) {
      undoBtn && (undoBtn.text = "L{UndoRedo.Undo}");
      redoBtn && (redoBtn.text = "L{UndoRedo.Redo}");
    }
    return super.changeItems(items2);
  }
  updateProject(project) {
    this.stm = project.stm;
  }
  fillUndoRedoCombo() {
    const { transactionsCombo } = this.widgetMap;
    transactionsCombo && (transactionsCombo.items = [[0, "Original data"], ...this.stm.queue.map((title, idx) => [idx + 1, title || `Transaction ${idx + 1}`])]);
  }
  updateUndoRedoControls() {
    const {
      stm,
      showZeroActionBadge
    } = this, {
      undoBtn,
      redoBtn,
      transactionsCombo
    } = this.widgetMap;
    undoBtn.badge = stm.position || (showZeroActionBadge ? "0" : "");
    redoBtn.badge = stm.length - stm.position || (showZeroActionBadge ? "0" : "");
    undoBtn.disabled = !stm.canUndo;
    redoBtn.disabled = !stm.canRedo;
    this.fillUndoRedoCombo();
    if (transactionsCombo) {
      transactionsCombo.disabled = transactionsCombo.store.count <= 1;
    }
  }
  transactionsDisplayValueRenderer(record, combo) {
    var _a5;
    const stmPos = ((_a5 = this.stm) == null ? void 0 : _a5.position) || 0;
    return `${stmPos} undo actions / ${combo.store.count - stmPos - 1} redo actions`;
  }
  onUndo() {
    const { stm, project } = this;
    if (stm.canUndo) {
      if (stm.revisionsEnabled) {
        project.queue(() => {
          stm.undo();
          return project.commitAsync();
        });
      } else {
        stm.undo();
      }
    }
  }
  onRedo() {
    const { stm, project } = this;
    if (stm.canRedo) {
      if (stm.revisionsEnabled) {
        project.queue(() => {
          stm.redo();
          return project.commitAsync();
        });
      } else {
        stm.redo();
      }
    }
  }
  onTransactionSelected(combo) {
    const { stm } = this, delta = stm.position - combo.value;
    if (delta > 0) {
      stm.canUndo && stm.undo(delta);
    } else if (delta < 0) {
      stm.canRedo && stm.redo(-delta);
    }
  }
};
__publicField(UndoRedoBase, "$name", "UndoRedoBase");
__publicField(UndoRedoBase, "type", "undoredobase");
UndoRedoBase.initClass();
UndoRedoBase._$name = "UndoRedoBase";

// ../Core/lib/Core/widget/graph/Histogram.js
var ns = "http://www.w3.org/2000/svg";
var typePrio = {
  bar: 1,
  outline: 2,
  text: 3
};
var byOrder = (l, r) => parseInt(l.order, 10) - parseInt(r.order, 10);
var byDatasetOrder = (l, r) => parseInt(l.dataset.order, 10) - parseInt(r.dataset.order, 10);
var getField = (s) => s.field;
var returnFalse = () => false;
var classesDelimiter = /\s+/;
var Histogram = class extends Widget {
  static get configurable() {
    return {
      /**
       * An array of data objects used to drive the histogram. The property/properties used
       * are defined in the {@link #config-series} option.
       * @config {Object[]}
       * @default
       */
      data: null,
      /**
       * The values to represent in bar form.
       * @config {Number[]}
       */
      values: null,
      /**
       * Object enumerating data series for the histogram.
       * The object keys are treated as series identifiers and values are objects that
       * can contain the following properties:
       *  - `type` A String, either `'bar'` or `'outline'`
       *  - `field` A String, the name of the property to use from the data objects in the {@link #config-data} option.
       * If the value is omitted the series identifier is used as the property name.
       * @config {Object<String, HistogramSeries>}
       */
      series: null,
      /**
       * By default, the bars are scaled based upon the detected max value across all the series.
       * A specific top value to represent the 100% height may be configured.
       * @config {Number}
       */
      topValue: null,
      element: {
        children: [{
          ns,
          tag: "svg",
          reference: "svgElement",
          width: "100%",
          height: "100%",
          preserveAspectRatio: "none",
          children: [{
            ns,
            tag: "g",
            reference: "scaledSvgGroup"
          }, {
            ns,
            tag: "g",
            reference: "unscaledSvgGroup"
          }]
        }]
      },
      /**
       * By default, all bars are rendered, even those with zero height. Configure this as `true`
       * to omit zero height bars.
       * @config {Boolean}
       * @default
       */
      omitZeroHeightBars: false,
      /**
       * By default, the histogram calls {@link #config-getBarText} once per each datum.
       * So the function is supposed to output all the series values the way it needs.
       * Configure this as `false` to call the function for each series value
       * if you need to display the values separately or having different styling.
       * @config {Boolean}
       * @default
       */
      singleTextForAllBars: true,
      monitorResize: true,
      /**
       * A Function which returns a CSS class name to add to a rectangle element.
       * The following parameters are passed:
       * @config {Function}
       * @param {HistogramSeries} series The series being rendered
       * @param {DomConfig} domConfig The rectangle configuration object
       * @param {Object} datum The datum being rendered
       * @param {Number} index The index of the datum being rendered
       * @returns {String} CSS class name of the rectangle element
       * @deprecated Use {@link #config-getBarClass} instead. This config will be removed in v7.0.
       */
      getRectClass: void 0,
      /**
       * A Function which returns a CSS class name to add to a rectangle element
       * representing a "bar"-type series value.
       * The following parameters are passed:
       * @config {Function}
       * @param {HistogramSeries} series The series being rendered
       * @param {DomConfig} domConfig The rectangle element configuration object
       * @param {Object} datum The datum being rendered
       * @param {Number} index The index of the datum being rendered
       * @returns {String} CSS class name of the rectangle element
       */
      getBarClass(series, domConfig, datum, index) {
        return "";
      },
      /**
       * A Function which returns a CSS class name to add to a path element
       * built for an `outline` type series.
       * The following parameters are passed:
       * @param {HistogramSeries} series The series being rendered
       * @param {Object[]} data The series data
       * @returns {String} CSS class name of the path element
       * @config {Function}
       */
      getOutlineClass(series, data) {
        return "";
      },
      /**
       * A Function which if provided should return a DOM configuration object for
       * a `path` element built for an `outline` type series.
       * The function accepts a default prepared DOM configuration
       * in an argument which then can be processed and returned.
       *
       * The following parameters are passed to the function:
       * @param {HistogramSeries} series The series being rendered
       * @param {DomConfig} domConfig The `path` element DOM configuration object
       * @param {Object[]} data The series data
       * @returns {Object} Resulting DOM configuration object. If no value returned the path is
       * not displayed.
       * @config {Function}
       */
      getOutlineDOMConfig(series, domConfig, data) {
        return domConfig;
      },
      /**
       * A Function which returns the tooltip text to display when hovering a bar.
       * The following parameters are passed:
       *
       * @config {Function}
       * @param {HistogramSeries} series The series being rendered
       * @param {DomConfig} domConfig The rectangle configuration object
       * @param {Object} datum The datum being rendered
       * @param {Number} index The index of the datum being rendered
       * @returns {String} Tip for the bar.
       */
      getBarTip(series, domConfig, datum, index) {
      },
      /**
       * A Function which returns the text to render inside a bar.
       * The following parameters are passed:
       * @param {Object} datum The datum being rendered
       * @param {Number} index The index of the datum being rendered
       * @param {HistogramSeries} series The series (provided if {@link #config-singleTextForAllBars}
       * is `false`)
       * @returns {String} Text to render in the bar.
       * @config {Function}
       */
      getBarText(datum, index, series) {
        return "";
      },
      /**
       * A Function which if provided should return a DOM configuration object for a bar
       * (a `RECT` element representing a single "bar"-type value).
       * The function accepts a default prepared DOM configuration
       * in an argument which then can be processed and returned.
       *
       * The following parameters are passed to the function:
       * @param {HistogramSeries} series The series being rendered
       * @param {DomConfig} domConfig The rectangle DOM configuration object
       * @param {Object} datum The datum being rendered
       * @param {Number} index The index of the datum being rendered
       * @returns {Object} Resulting DOM configuration object. If no value returned the bar is
       * not displayed.
       * @config {Function}
       */
      getBarDOMConfig: null,
      /**
       * A Function which if provided returns DOM configuration for a bar text
       * (a `TEXT` element accompanying a single "bar"-type value).
       * The function accepts a default prepared DOM configuration
       * in the first argument which then can be processed and returned.
       *
       * The following parameters are passed to the function:
       * @param {DomConfig} domConfig The text element DOM configuration object
       * @param {Object} datum The datum being rendered
       * @param {Number} index The index of the datum being rendered
       * @param {HistogramSeries} series The series (provided if {@link #config-singleTextForAllBars}
       * is `false`)
       * @returns {Object} Resulting DOM configuration object. If no value returned the text is
       * not displayed.
       * @config {Function}
       */
      getBarTextDOMConfig(domConfig, datum, index, series) {
        return domConfig;
      },
      getBarTextTip(renderData, datum, index, series) {
      }
    };
  }
  //endregion
  //region Init
  construct(config) {
    const me = this;
    super.construct(config);
    me.scheduleRefresh = me.createOnFrame(me.refresh, [], me, true);
    me.refresh();
  }
  set tip(tip) {
    var _a5;
    const me = this;
    (_a5 = me.tip) == null ? void 0 : _a5.destroy();
    if (tip) {
      me._tip = Tooltip.new({
        owner: me,
        forElement: me.svgElement,
        forSelector: "rect",
        internalListeners: {
          beforeShow: "up.onBeforeTipShow"
        }
      }, tip);
    } else {
      me._tip = null;
    }
  }
  onElementResize() {
    super.onElementResize(...arguments);
    const svgRect = this.svgElement.getBoundingClientRect();
    this.scaledSvgGroup.setAttribute("transform", `scale(${svgRect.width} ${svgRect.height})`);
  }
  onBeforeTipShow({ source: tip }) {
    const index = parseInt(tip.activeTarget.dataset.index);
    tip.html = tip.contentTemplate({
      histogram: this,
      index
    });
  }
  updateSeries(value2) {
    const me = this, series = me._series = {};
    let index = 0, barSeriesCount = 0;
    for (const id2 in value2) {
      if (value2[id2] !== false) {
        const data = series[id2] = Objects.merge({}, value2[id2]);
        if (!data.field) {
          data.field = id2;
        }
        if (!data.type) {
          data.type = "bar";
        }
        if (!("order" in data)) {
          data.order = typePrio[data.type] * 10 + index;
        }
        if (!("index" in data)) {
          data.index = index;
        }
        if (data.type === "bar") {
          data.index = barSeriesCount++;
        }
        data.id = id2;
        index++;
      }
    }
    if (!me.topValue && me._data) {
      me.topValue = me.getDataTopValue(me._data);
    }
    if (!me.refreshSuspended) {
      me.scheduleRefresh();
    }
  }
  getDataTopValue(data, series) {
    const fields = Object.values(series || this.series).map(getField);
    let result = 0, datum;
    for (let i = 0, { length } = data; i < length; i++) {
      datum = data[i];
      for (let j = 0, { length: length2 } = fields; j < length2; j++) {
        result = Math.max(result, datum[fields[j]]);
      }
    }
    return result;
  }
  updateGetRectClass(value2) {
    VersionHelper.deprecate("core", "7.0.0", "getRectClass is deprecated in favor of getBarClass");
    this.getBarClass = value2;
  }
  updateData(data) {
    const me = this;
    me._data = data;
    if (!me.topValue && me._data && me._series) {
      me.topValue = me.getDataTopValue(data);
    }
    if (!me.refreshSuspended) {
      me.scheduleRefresh();
    }
  }
  updateTopValue(value2) {
    const me = this;
    me._topValue = value2;
    if (!value2 && me._data) {
      me._topValue = me.getDataTopValue(me._data);
    }
    if (!me.refreshSuspended) {
      me.scheduleRefresh();
    }
  }
  // Must exist from the start because configuration setters call it.
  // Once configured, will be replaced with a function which schedules a refresh for the next animation frame.
  scheduleRefresh() {
  }
  suspendRefresh() {
    this.refreshSuspended++;
  }
  resumeRefresh() {
    if (this.refreshSuspended) {
      this.refreshSuspended--;
    }
  }
  refresh(params) {
    const me = this, {
      series,
      _tip,
      topValue,
      singleTextForAllBars
    } = me, extraArgs = (params == null ? void 0 : params.args) || [], histogramElements = [], textElements = [];
    if (!series) {
      return;
    }
    const sortedSeriesData = Object.values(series).sort(byOrder);
    if (singleTextForAllBars) {
      for (const data of sortedSeriesData) {
        const elConfig = me[`draw${StringHelper.capitalize(data.type)}`](data, ...extraArgs);
        if (elConfig) {
          if (Array.isArray(elConfig)) {
            histogramElements.push.apply(histogramElements, elConfig);
          } else {
            histogramElements.push(elConfig);
          }
        }
      }
      textElements.push(...me.drawText(null, ...extraArgs));
    } else {
      for (const data of sortedSeriesData) {
        const elConfig = me[`draw${StringHelper.capitalize(data.type)}`](data, ...extraArgs);
        if (elConfig) {
          if (Array.isArray(elConfig)) {
            histogramElements.push.apply(histogramElements, elConfig);
          } else {
            histogramElements.push(elConfig);
          }
        }
        if (data.type === "bar") {
          textElements.push(...me.drawText(data, ...extraArgs));
        }
      }
      textElements.sort(byDatasetOrder);
    }
    histogramElements.sort(byDatasetOrder);
    DomSync.sync({
      domConfig: {
        width: "100%",
        height: "100%",
        preserveAspectRatio: "none",
        dataset: {
          topValue
        },
        children: [
          {
            ns,
            tag: "g",
            reference: "scaledSvgGroup",
            children: histogramElements
          },
          {
            ns,
            tag: "g",
            reference: "unscaledSvgGroup",
            children: textElements
          }
        ]
      },
      configEquality: returnFalse,
      targetElement: me.svgElement
    });
    if (_tip && _tip.isVisible) {
      me.onBeforeTipShow({ source: _tip });
    }
  }
  drawBar(series, ...args) {
    const me = this, {
      topValue,
      data,
      omitZeroHeightBars,
      barStyle
    } = me, {
      field,
      order,
      stretch
    } = series, { length } = data, defaultWidth = 1 / length, children = [], seriesId = StringHelper.createId(series.id), seriesIndex = series.index, forceHeight = stretch ? 1 : void 0;
    let width;
    for (let index = 0, x = 0, { length: length2 } = data; index < length2; index++, x += width) {
      const datum = data[index];
      let domConfig = datum.rectConfig = {
        ns,
        tag: "rect",
        dataset: {}
      };
      const value2 = datum[field], height = value2 ? forceHeight || datum.height || (value2 > topValue ? topValue : value2) / topValue : 0, y = 1 - height, barTip = me.callback("getBarTip", me, [series, domConfig, datum, index, ...args]);
      width = datum.width || defaultWidth;
      if (barStyle) {
        domConfig.style = barStyle;
      } else {
        delete domConfig.style;
      }
      Object.assign(domConfig.dataset, {
        index,
        order,
        series: seriesId
      });
      const rectClass = {
        [`b-series-${seriesId}`]: 1,
        [`b-series-index-${seriesIndex}`]: 1
      }, classes = me.callback("getBarClass", me, [series, domConfig, datum, index, ...args]);
      if (classes) {
        classes.split(classesDelimiter).forEach((cls) => rectClass[cls] = 1);
      }
      Object.assign(domConfig, {
        x,
        y,
        width,
        height,
        class: rectClass
      });
      if (barTip) {
        domConfig.dataset.btip = barTip;
      } else {
        delete domConfig.dataset.btip;
      }
      if (me.getBarDOMConfig) {
        domConfig = me.getBarDOMConfig(series, domConfig, datum, index, ...args);
      }
      if (domConfig && (domConfig.height || !omitZeroHeightBars)) {
        children.push(domConfig);
      }
    }
    return children;
  }
  changeGetBarDOMConfig(fn2) {
    return fn2 ? this.bindCallback(fn2) : null;
  }
  drawOutline(series, ...args) {
    const me = this, {
      topValue,
      data
    } = me, {
      field,
      order,
      id: id2
    } = series, defaultWidth = 1 / data.length, coords = ["M 0,1"], domConfig = {
      ns,
      tag: "path",
      dataset: {
        order,
        id: id2
      }
    };
    let barWidth, command1 = "M", command2 = "L";
    for (let i = 0, x = 0, { length } = data; i < length; i++) {
      const barHeight = 1 - data[i][field] / topValue;
      barWidth = data[i].width || defaultWidth;
      coords.push(`${command1} ${x},${barHeight} ${command2} ${x += barWidth},${barHeight}`);
      command1 = command2 = "";
    }
    domConfig.class = `b-series-${series.id} b-series-index-${series.index} ` + me.callback("getOutlineClass", me, [series, data, ...args]);
    domConfig.d = coords.join(" ");
    return me.callback("getOutlineDOMConfig", me, [series, domConfig, data, ...args]) || [];
  }
  drawText(series, ...args) {
    const me = this, { data } = me, defaultWidth = 1 / data.length, defaultY = "100%", unscaledSvgGroups = [];
    for (let index = 0, width, x = 0, { length } = data; index < length; index++, x += width) {
      width = data[index].width || defaultWidth;
      const barText = me.callback("getBarText", me, [data[index], index, series, ...args]);
      if (barText) {
        const domConfig = me.callback("getBarTextDOMConfig", me, [
          {
            ns,
            tag: "text",
            className: "b-bar-legend",
            html: barText,
            left: x,
            width,
            x: `${(x + width / 2) * 100}%`,
            y: data[index].y !== void 0 ? data[index].y : defaultY,
            dataset: {
              index,
              series
            }
          },
          data[index],
          index,
          series,
          ...args
        ]);
        if (domConfig) {
          const barTip = me.callback("getBarTextTip", me, [domConfig, data[index], index, series, ...args]);
          if (barTip) {
            domConfig.dataset.btip = barTip;
          } else {
            delete domConfig.dataset.btip;
          }
          unscaledSvgGroups.push(domConfig);
        }
      }
    }
    return unscaledSvgGroups;
  }
  //endregion
};
//region Config
__publicField(Histogram, "type", "histogram");
__publicField(Histogram, "$name", "Histogram");
__publicField(Histogram, "properties", {
  refreshSuspended: 0
});
Histogram.initClass();
Histogram._$name = "Histogram";

// ../Core/lib/Core/widget/graph/Scale.js
var ns2 = "http://www.w3.org/2000/svg";
var Scale = class extends Widget {
  static get configurable() {
    return {
      scalePoints: null,
      // Padding after the max scale point.
      // Expressed as the share of the height.
      scaleMaxPadding: 0.1,
      /**
       * Configure as `true` to create a horizontal scale. Scales are vertical by default.
       * @config {Boolean}
       */
      horizontal: false,
      /**
       * Side to align the scale to. Defaults to `bottom` for {@link #config-horizontal} Scales
       * and `right` for vertical Scales.
       * @config {String}
       */
      align: {
        value: false,
        $config: {
          merge: "replace"
        }
      },
      element: {
        children: [{
          ns: ns2,
          tag: "svg",
          reference: "svgElement",
          width: "100%",
          height: "100%",
          preserveAspectRatio: "none",
          children: [{
            ns: ns2,
            tag: "g",
            reference: "scaledSvgGroup",
            children: [{
              ns: ns2,
              tag: "path",
              reference: "pathElement"
            }]
          }, {
            ns: ns2,
            tag: "g",
            reference: "unscaledSvgGroup"
          }]
        }]
      },
      monitorResize: true
    };
  }
  //endregion
  //region Init
  construct(config) {
    super.construct(config);
    this.scheduleRefresh = this.createOnFrame(this.refresh, [], this, true);
    this.refresh();
  }
  changeAlign(align) {
    if (!align) {
      align = this.horizontal ? "bottom" : "right";
    }
    return align;
  }
  updateAlign(align, oldAlign) {
    this.element.classList.remove(`b-align-${oldAlign}`);
    this.element.classList.add(`b-align-${align}`);
  }
  updateHorizontal(horizontal, oldHorizontal) {
    this.element.classList.remove(`b-scale-${oldHorizontal ? "horizontal" : "vertical"}`);
    this.element.classList.add(`b-scale-${horizontal ? "horizontal" : "vertical"}`);
  }
  onElementResize() {
    super.onElementResize(...arguments);
    this.scheduleRefresh();
  }
  // Must exist from the start because configuration setters call it.
  // Once configured, will be replaced with a function which schedules a refresh for the next animation frame.
  scheduleRefresh() {
  }
  refresh() {
    var _a5;
    if (!this.scalePoints) {
      return;
    }
    const me = this, {
      horizontal,
      width,
      height,
      align,
      scalePoints,
      scaleMaxPadding
    } = me, scaleMax = (_a5 = scalePoints[scalePoints.length - 1]) == null ? void 0 : _a5.value, path = [], labels = [];
    const posFactor = 1 / (scaleMax + scaleMaxPadding * scaleMax);
    me.scaledSvgGroup.setAttribute("transform", `scale(${horizontal ? width : 1} ${horizontal ? 1 : height})`);
    for (const point of scalePoints) {
      const isLabelStep = Boolean(point.text), pos = posFactor * point.value;
      if (isLabelStep) {
        const label = {
          ns: ns2,
          tag: "text",
          className: "b-scale-tick-label",
          html: point.text,
          dataset: {
            tick: point.value
          }
        };
        if (horizontal) {
          label.x = `${pos * 100}%`;
          label.y = align === "top" ? "1.6em" : height - 12;
        } else {
          label.x = align === "left" ? "12" : `${width - 12}`;
          label.y = `${(1 - pos) * 100}%`;
        }
        labels.push(label);
      }
      if (horizontal) {
        if (align === "top") {
          path.push(`M${pos},0 L${pos},${isLabelStep ? 10 : 5}`);
        } else {
          path.push(`M${pos},${height} L${pos},${height - (isLabelStep ? 10 : 5)}`);
        }
      } else {
        if (align === "left") {
          path.push(`M0,${1 - pos} L${isLabelStep ? 10 : 5},${1 - pos}`);
        } else {
          path.push(`M${width},${1 - pos} L${width - (isLabelStep ? 10 : 5)},${1 - pos}`);
        }
      }
    }
    me.pathElement.setAttribute("d", path.join(""));
    DomSync.syncChildren({
      domConfig: { children: labels }
    }, me.unscaledSvgGroup);
  }
  //endregion
};
//region Config
__publicField(Scale, "$name", "Scale");
__publicField(Scale, "type", "scale");
Scale.initClass();
Scale._$name = "Scale";

// ../Core/lib/Core/widget/mixin/Minifiable.js
var Minifiable_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    compose() {
      const { minified } = this;
      return {
        class: {
          "b-minified": minified
        }
      };
    }
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "Minifiable"), __publicField(_a5, "configurable", {
    /**
     * Set to `false` to prevent this widget from assuming its {@link #config-minified} form automatically (for
     * example, due to {@link Core.widget.Toolbar#config-overflow} handling.
     *
     * When this value is `true` (the default), the minifiable widget's {@link #config-minified} config may be
     * set to `true` to reduce toolbar overflow.
     *
     * @config {Boolean}
     * @default
     */
    minifiable: true,
    /**
     * Set to `true` to present this widget in its minimal form.
     * @config {Boolean}
     * @default false
     */
    minified: null
  }), _a5;
};

// ../Core/lib/Core/widget/mixin/Responsive.js
var EMPTY2 = [];
var isStateName = (name) => name[0] !== "*";
var pop = (object, key) => {
  const ret = object[key] || null;
  delete object[key];
  return ret;
};
var responsiveRootFn = (widget) => widget.responsiveRoot;
var scoring = {
  number: (threshold) => ({ width }) => width <= threshold && threshold
};
var splitConfigs = (configs) => {
  delete configs.once;
  return {
    configs,
    callback: pop(configs, "callback"),
    when: pop(configs, "when")
  };
};
var splitMergedConfigs = (initialConfig, cls, ...parts) => {
  const once = parts.flatMap((p) => (p == null ? void 0 : p.once) || EMPTY2), configs = cls.mergeConfigs(...parts), ret = splitConfigs(configs);
  ret.once = once.length ? splitConfigs(cls.mergeConfigs(...once)) : null;
  for (const key in configs) {
    if (key in initialConfig) {
      delete configs[key];
    }
  }
  return ret;
};
var wrapWidget = (widget, handler) => {
  let triggers, desc = Proxy.revocable(widget, {
    get(o, name) {
      if (triggers) {
        triggers[name] = true;
      }
      return widget[name];
    }
  }), detacher2 = FunctionHelper.after(widget, "onConfigChange", (ignore, { name }) => {
    if (triggers == null ? void 0 : triggers[name]) {
      handler();
    }
  }), resizer = widget.ion({
    resize: () => {
      handler();
    }
  });
  widget.monitorResize = true;
  return {
    widget,
    get object() {
      return desc == null ? void 0 : desc.proxy;
    },
    destroy() {
      if (desc) {
        desc.revoke();
        detacher2();
        resizer();
        desc = detacher2 = resizer = null;
      }
    },
    reset() {
      triggers = /* @__PURE__ */ Object.create(null);
    }
  };
};
var Responsive_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base).mixin(Delayable_default, Fencible_default) {
    get isResponsivePending() {
      return this.responsiveUpdateCount === 0 && this.hasConfig("responsive");
    }
    get isResponsiveUpdating() {
      var _a6;
      return this._responsiveUpdating || ((_a6 = this.responsiveWidget) == null ? void 0 : _a6._responsiveUpdating);
    }
    // responsive
    updateResponsive(responsive) {
      var _a6;
      const me = this, cls = me.constructor, { initialConfig, responsiveDefaults } = me, stateNames = Array.from(
        new Set(ObjectHelper.keys(responsive).concat(ObjectHelper.keys(responsiveDefaults)))
      ).filter(isStateName);
      let states = null, hasWhen, name, state, when;
      if (responsive) {
        states = {
          "*": splitMergedConfigs(initialConfig, cls, responsiveDefaults["*"], responsive["*"])
        };
        for (name of stateNames) {
          state = responsive[name];
          if (state !== null && state !== false) {
            hasWhen = hasWhen || state && "when" in state;
            states[name] = splitMergedConfigs(
              initialConfig,
              cls,
              responsiveDefaults["*"],
              responsiveDefaults[name],
              responsive["*"],
              state
            );
            when = states[name].when;
            states[name].when = ((_a6 = scoring[typeof when]) == null ? void 0 : _a6.call(scoring, when)) || when;
          }
        }
      }
      me.$responsiveStates = states;
      me.$responsiveWhen = hasWhen;
      me.syncResponsiveWidget();
    }
    // responsiveState
    updateResponsiveState(state, oldState) {
      var _a6, _b2, _c2, _d;
      const me = this, { $responsiveStates: states } = me, initial = ++me.responsiveStateChanges === 1, classList = (_a6 = me.element) == null ? void 0 : _a6.classList, defaults = states["*"], def = states[state] || defaults, once = initial && (def.once || defaults.once), isStateful = initial && me.isStateful, target = me.responsiveWidget;
      let config = def.configs, otherConfigs = once == null ? void 0 : once.configs, callback;
      if (otherConfigs) {
        config = config ? me.constructor.mergeConfigs(config, otherConfigs) : otherConfigs;
      }
      oldState && (classList == null ? void 0 : classList.remove(`b-responsive-${oldState.toLowerCase()}`));
      state && (classList == null ? void 0 : classList.add(`b-responsive-${state.toLowerCase()}`));
      if (isStateful) {
        otherConfigs = me.loadStatefulData();
        if (otherConfigs) {
          config = config ? me.constructor.mergeConfigs(config, otherConfigs) : otherConfigs;
        }
        me.suspendStateful();
      }
      me._responsiveUpdating = true;
      try {
        (_b2 = me.trigger) == null ? void 0 : _b2.call(me, "beforeResponsiveStateChange", { state, oldState, target });
        config && me.setConfig(config);
        for (callback of [def.callback, once == null ? void 0 : once.callback]) {
          (_c2 = typeof callback === "string" ? me[callback] : callback) == null ? void 0 : _c2.call(
            me,
            { source: me, state, oldState, target, initial }
          );
        }
        (_d = me.trigger) == null ? void 0 : _d.call(me, "responsiveStateChange", { state, oldState, target });
      } finally {
        me._responsiveUpdating = false;
        isStateful && me.resumeStateful();
      }
    }
    // responsiveTarget
    get responsiveTarget() {
      return this.responsiveWidget || this._responsiveTarget;
    }
    updateResponsiveTarget() {
      this.syncResponsiveWidget();
    }
    // responsiveWidget
    updateResponsiveWidget(target) {
      var _a6;
      const me = this, responsiveUpdate = target && me.responsiveUpdate;
      (_a6 = me.$responsiveWrapper) == null ? void 0 : _a6.destroy();
      me.$responsiveWrapper = target && wrapWidget(target, responsiveUpdate);
      responsiveUpdate == null ? void 0 : responsiveUpdate.now();
    }
    // Support methods
    responsiveUpdate() {
      const me = this, { $responsiveStates: states, $responsiveWrapper: wrapper } = me, responsiveTarget = wrapper == null ? void 0 : wrapper.widget;
      if (states && wrapper) {
        let best = null, bestScore = 0, fromWhen = states, score, state;
        if (responsiveTarget && responsiveTarget !== me && !me.$responsiveWhen) {
          responsiveTarget.getConfig("responsive");
          fromWhen = responsiveTarget.$responsiveStates || fromWhen;
        }
        wrapper.reset();
        for (state in states) {
          if (state !== "*") {
            score = fromWhen[state].when(wrapper.object, BrowserHelper);
            if (score != null && score !== false && (!best || score < bestScore)) {
              best = state;
              bestScore = score;
            }
          }
        }
        ++me.responsiveUpdateCount;
        me.responsiveState = best;
      }
    }
    syncResponsiveWidget() {
      var _a6;
      const me = this;
      let widget = null, responsiveTarget;
      if (!me.isDestroying && me.responsive) {
        responsiveTarget = me.responsiveTarget;
        if (!(widget = responsiveTarget)) {
          widget = !me.responsiveRoot && ((_a6 = me.up) == null ? void 0 : _a6.call(me, responsiveRootFn)) || me;
        } else if (typeof responsiveTarget === "string") {
          widget = responsiveTarget === "@" ? me : responsiveTarget[0] === "@" ? me[responsiveTarget.substring(1)] : me.up(responsiveTarget);
          if (!widget) {
            throw new Error(`No match for responsiveTarget="${responsiveTarget}"`);
          }
        }
        if (!widget.isWidget) {
          throw new Error(`${widget.constructor.$$name} is not a widget and cannot be a responsiveTarget`);
        }
      }
      me.responsiveWidget = widget;
      return widget;
    }
  }, __publicField(_a5, "$name", "Responsive"), __publicField(_a5, "configurable", {
    /**
     * Specifies the various responsive state objects keyed by their name. Each key (except `'*'`, see below) in
     * this object is a state name (see {@link #config-responsiveState}) and its corresponding value is the
     * associated {@link #typedef-ResponsiveState} object.
     *
     * Some properties of a `ResponsiveState` object are special, for example `when` and `callback`. All other
     * properties of the state object are config properties to apply when that state is active.
     *
     * The `when` property can be a function that computes the score for the state. The state whose `when` function
     * returns the lowest score is selected and its non-special properties will be assigned to the instance. If
     * `when` is a number, it will be converted into a scoring function (see below).
     *
     * A `when` function accepts two readonly parameters and returns either a numeric score if the state should be
     * considered, or `false` or `null` if the state should be ignored (i.e., it does match with the current state).
     *
     * The first parameter is a readonly proxy for the {@link #config-responsiveTarget widget} whose size and other
     * properties determine the state's score. The proxy tracks property access to that widget in order to update
     * the responsive state should any of those properties change.
     *
     * The second argument to a `when` function is the {@link Core.helper.BrowserHelper} singleton. This allows
     * a `when` function to conveniently test platform and browser information.
     *
     * The state whose `when` function returns the lowest score is selected as the new
     * {@link #config-responsiveState} and its config object (minus the `when` function and other special
     * properties) is applied to the instance.
     *
     * If `when` is a number, it is converted to function. The following two snippets produce the same `when`
     * scoring:
     *
     * ```javascript
     *      small : {
     *          when : 400,
     *          ...
     *      }
     * ```
     *
     * The above converted to:
     *
     * ```javascript
     *      small : {
     *          when : ({ width }) => width <= 400 && 400,
     *          ...
     *      }
     * ```
     * Selecting the lowest score as the winner allows for the simple conversion of width threshold to score value,
     * such that the state with the smallest matching width is selected.
     *
     * If the `responsive` config object has an asterisk key (`'*'`), its value is used as the default set of config
     * properties to apply all other states. This will be the only config properties to apply if no `when` function
     * returns a score. In this way, this special state object acts as a default state as well as a set of
     * default values for other states to share. This state object has no `when` function.
     *
     * The default for this config is:
     * ```javascript
     *  {
     *      small : {
     *          when : 400
     *      },
     *
     *      medium : {
     *          when : 800
     *      },
     *
     *      large : {
     *          when : () => Infinity
     *      },
     *
     *      '*' : {}
     *  }
     * ```
     *
     * A derived class (or instance) can use these states by populating other config properties, define
     * additional states, and/or adjust the `when` properties to use different size thresholds.
     *
     * @config {Object}
     */
    responsive: {
      $config: {
        lazy: "paint"
      },
      value: null
    },
    /**
     * The defaults for the {@link #config-responsive} config. These are separated so that the act of setting the
     * {@link #config-responsive} config is what triggers additional processing.
     * @config {Object}
     * @internal
     * @default
     */
    responsiveDefaults: {
      small: {
        when: 400
      },
      medium: {
        when: 800
      },
      large: {
        when: () => Infinity
      },
      "*": {}
    },
    /**
     * Set to `true` to mark this instance as the default {@link #config-responsiveTarget} for descendants that do
     * not specify an explicit {@link #config-responsiveTarget} of their own.
     * @config {Boolean}
     * @default false
     */
    responsiveRoot: null,
    /**
     * The name of the active state of the {@link #config-responsive} config. This is assigned internally
     * and should not be assigned directly.
     *
     * @config {String}
     * @readonly
     */
    responsiveState: null,
    /**
     * The widget whose size and other properties drive this object's responsive behavior. If this config is not
     * specified, the closest ancestor that specified {@link #config-responsiveRoot responsiveRoot=true} will be
     * used. If there is no such ancestor, then the instance using this mixin is used.
     *
     * If this value is set to `'@'`, then this instance is used even if there is a {@link #config-responsiveRoot}
     * ancestor.
     *
     * If this config is a string that starts with `'@'`, the text following the first character is the name of the
     * property on this instance that holds the target to use. For example, `'@owner'` to use the value of the
     * `owner` property as the responsive target.
     *
     * If this config is a string that does not start with `'@'`, that string is passed to
     * {@link Core.widget.Widget#function-up} to find the closest matching ancestor.
     *
     * If another widget is used as the `responsiveTarget` and if this instance does not specify any explicit `when`
     * properties in its {@link #config-responsive} config, then the `when` definitions of the `responsiveTarget`
     * will be used for this instance.
     * @config {String|Core.widget.Widget}
     */
    responsiveTarget: {
      value: null,
      $config: {
        lazy: "paint"
      }
    },
    responsiveWidget: {
      value: null,
      $config: {
        nullify: true
      }
    }
  }), __publicField(_a5, "delayable", {
    responsiveUpdate: "raf"
  }), __publicField(_a5, "fenced", {
    syncResponsiveWidget: true
  }), __publicField(_a5, "prototypeProperties", {
    responsiveStateChanges: 0,
    responsiveUpdateCount: 0
  }), _a5;
};

// ../Core/lib/Core/widget/mixin/Styleable.js
var Styleable_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    changeCssVarPrefix(prefix) {
      ObjectHelper.assertString(prefix, "prefix");
      if (prefix && !prefix.endsWith("-")) {
        prefix = prefix + "-";
      }
      return prefix || "";
    }
    changeCss(css) {
      ObjectHelper.assertObject(css, "css");
      const me = this;
      if (!globalThis.Proxy) {
        throw new Error("Proxy not supported");
      }
      const proxy = new Proxy({}, {
        get(target, property) {
          var _a6;
          const styles2 = getComputedStyle(me.element || document.documentElement);
          return (_a6 = styles2.getPropertyValue(`--${me.cssVarPrefix}${StringHelper.hyphenate(property)}`)) == null ? void 0 : _a6.trim();
        },
        set(target, property, value2) {
          const element = me.element || document.documentElement;
          element.style.setProperty(`--${me.cssVarPrefix}${StringHelper.hyphenate(property)}`, value2);
          return true;
        }
      });
      if (css) {
        if (me._element) {
          ObjectHelper.assign(proxy, css);
        } else {
          me.$initialCSS = css;
        }
      }
      return proxy;
    }
    // Apply any initially supplied CSS when we have an element
    updateElement(element, ...args) {
      super.updateElement(element, ...args);
      if (this.$initialCSS) {
        ObjectHelper.assign(this.css, this.$initialCSS);
      }
    }
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "Styleable"), __publicField(_a5, "configurable", {
    /**
     * CSS variable prefix, appended to the keys used in {@link #config-css}.
     *
     * For example:
     *
     * ```javascript
     * {
     *    cssVarPrefix : 'taskboard',
     *
     *    css : {
     *        cardBackground : '#333'
     *    }
     * }
     * ```
     *
     * Results in the css var `--taskboard-card-background` being set to `#333`.
     * @config {String}
     * @category CSS
     */
    cssVarPrefix: "",
    /**
     * Allows runtime manipulating of CSS variables.
     *
     * See {@link #config-css} for more information.
     *
     * ```javascript
     * taskBoard.css.columnBackground = '#ccc';
     *
     * // Will set "--taskboard-column-background : #ccc"
     * ```
     *
     * @member {Proxy} css
     * @typings {typeof Proxy}
     * @category DOM
     */
    /**
     * Initial CSS variables to set.
     *
     * Each key will be applied as a CSS variable to the target elements style. Key names are hyphenated and
     * prefixed with {@link #config-cssVarPrefix} in the process. For example:
     *
     * ```javascript
     * {
     *    cssVarPrefix : 'taskboard',
     *
     *    css : {
     *        cardBackground : '#333'
     *    }
     * }
     * ```
     *
     * Results in the css var `--taskboard-card-background` being set to `#333`.
     *
     * @config {Object<String,String>}
     * @category CSS
     */
    css: {}
  }), _a5;
};

// ../Core/lib/Core/widget/util/AvatarRendering.js
var _AvatarRendering = class _AvatarRendering extends Base {
  static get configurable() {
    return {
      /**
       * Element used to listen for load errors. Normally the owning widgets own element.
       * @config {HTMLElement}
       */
      element: null,
      /**
       * Prefix prepended to a supplied color to create a CSS class applied when showing initials.
       * @config {String}
       * @default
       */
      colorPrefix: "b-sch-",
      /**
       * A tooltip config object to enable using a custom tooltip for the avatars. Listen for `beforeShow` and set
       * your html there.
       * @config {TooltipConfig}
       */
      tooltip: null,
      /**
       * The requested size of the avatars. If not null then the size is added as width and height to the avatar
       * element whether it is image or initials container
       * @config {String|Number|null}
       */
      size: null
    };
  }
  doDestroy() {
    var _a5;
    (_a5 = this.tooltip) == null ? void 0 : _a5.destroy();
    super.doDestroy();
  }
  updateElement(element) {
    EventHelper.on({
      element,
      delegate: ".b-resource-image",
      error: "onImageErrorEvent",
      thisObj: this,
      capture: true
    });
  }
  changeTooltip(config) {
    return Tooltip.new({
      forElement: this.element,
      forSelector: ".b-resource-avatar",
      cls: "b-resource-avatar-tooltip"
    }, config);
  }
  static get failedUrls() {
    return this._failedUrls || (this._failedUrls = /* @__PURE__ */ new Set());
  }
  /**
   * Returns a DOM config object containing a resource avatar, icon or resource initials. Display priority in that
   * order.
   * @param {AvatarConfig|AvatarConfig[]} options A single avatar config object or an array of the same.
   * @returns {DomConfig}
   */
  getResourceAvatar(options) {
    if (Array.isArray(options)) {
      return options.map((item) => this.getResourceAvatar(item));
    }
    const { initials, color, iconCls, imageUrl, defaultImageUrl, dataset = {}, resourceRecord, alt: alt2 = StringHelper.encodeHtml(resourceRecord == null ? void 0 : resourceRecord.name) } = options, config = this.getImageConfig(initials, color, imageUrl, defaultImageUrl, dataset, alt2) || this.getIconConfig(iconCls, dataset, alt2) || this.getResourceInitialsConfig(initials, color, dataset, alt2), { size } = this;
    Object.assign(config.style, {
      ...size ? { height: size, width: size } : void 0
    });
    return config;
  }
  getImageConfig(initials, color, imageUrl, defaultImageUrl, dataset, alt2) {
    imageUrl = _AvatarRendering.failedUrls.has(imageUrl) ? defaultImageUrl : imageUrl || defaultImageUrl;
    if (imageUrl) {
      return {
        tag: "img",
        draggable: "false",
        loading: "lazy",
        class: {
          "b-resource-avatar": 1,
          "b-resource-image": 1
        },
        style: {},
        alt: alt2,
        "aria-label": alt2,
        elementData: {
          defaultImageUrl,
          imageUrl,
          initials,
          color,
          dataset
        },
        src: imageUrl,
        dataset
      };
    }
  }
  getIconConfig(iconCls, dataset, name) {
    if (iconCls) {
      return iconCls && {
        tag: "i",
        style: {},
        "aria-label": name,
        class: {
          "b-resource-avatar": 1,
          "b-resource-icon": 1,
          [iconCls]: 1
        },
        dataset
      };
    }
  }
  getResourceInitialsConfig(initials, color, dataset, name) {
    const namedColor = DomHelper.isNamedColor(color) && color, hexColor = !namedColor && color, { size } = this;
    return {
      tag: "div",
      "aria-label": name,
      class: {
        "b-resource-avatar": 1,
        "b-resource-initials": 1,
        [`${this.colorPrefix}${namedColor}`]: namedColor
      },
      style: {
        backgroundColor: hexColor || null,
        ...size ? { height: size, width: size } : void 0
      },
      text: initials,
      dataset
    };
  }
  onImageErrorEvent({ target }) {
    if (!target.matches(".b-resource-avatar") || !target.isConnected) {
      return;
    }
    const { defaultImageUrl, initials, color, imageUrl, dataset } = target.elementData;
    if (defaultImageUrl && !target.src.endsWith(defaultImageUrl.replace(/^[./]*/gm, ""))) {
      target.src = defaultImageUrl;
    } else {
      const { parentElement } = target, targetIdx = Array.from(parentElement.childNodes).indexOf(target), domConfig = this.getResourceInitialsConfig(initials, color, dataset, target.alt), initialsEl = DomHelper.createElement(domConfig);
      if (target.$refOwnerId) {
        initialsEl.$refOwnerId = target.$refOwnerId;
      }
      initialsEl.elementData = target.elementData;
      initialsEl.lastDomConfig = domConfig;
      parentElement.lastDomConfig && (parentElement.lastDomConfig.children[targetIdx] = domConfig);
      parentElement.replaceChild(initialsEl, target);
    }
    _AvatarRendering.failedUrls.add(imageUrl);
  }
};
__publicField(_AvatarRendering, "$name", "AvatarRendering");
var AvatarRendering = _AvatarRendering;
AvatarRendering._$name = "AvatarRendering";

// ../Core/bundle/entry.thin.js
if (window && !window.bryntum) {
  window.bryntum = {};
}

// ../Grid/lib/Grid/util/GridLocation.js
var _GridLocation = class _GridLocation {
  /**
   * The grid which this `GridLocation` references.
   * @config {Grid.view.Grid} grid
   */
  /**
   * The record which this `GridLocation` references. (unless {@link #config-rowIndex} is used to configure)
   * @config {Core.data.Model} record
   */
  /**
   * The row index which this `GridLocation` references. (unless {@link #config-record} is used to configure).
   *
   * `-1` means the header row, in which case the {@link #config-record} will be `null`.
   * @config {Number} rowIndex
   */
  /**
   * The Column which this `GridLocation` references. (unless {@link #config-columnIndex} or {@link #config-columnId} is used to configure)
   * @config {Grid.column.Column} column
   */
  /**
   * The column id which this `GridLocation` references. (unless {@link #config-column} or {@link #config-columnIndex} is used to configure)
   * @config {String|Number} columnId
   */
  /**
   * The column index which this `GridLocation` references. (unless {@link #config-column} or {@link #config-columnId} is used to configure)
   * @config {Number} columnIndex
   */
  /**
   * The field of the column index which this `GridLocation` references. (unless another column identifier is used to configure)
   * @config {String} field
   */
  /**
   * Initializes a new GridLocation.
   * @param {GridLocationConfig|HTMLElement} location A grid location specifier. This may be:
   *  * An element inside a grid cell or a grid cell.
   *  * An object identifying a cell location using the following properties:
   *    * grid
   *    * record
   *    * rowIndex
   *    * column
   *    * columnIndex
   * @function constructor
   */
  constructor(location2) {
    if (location2) {
      if (location2.isLocation) {
        return location2;
      }
      if (location2.nodeType === Node.ELEMENT_NODE) {
        const grid = Widget.fromElement(location2, "gridbase"), cell = grid && location2.closest(grid.focusableSelector);
        if (cell) {
          const { dataset } = cell.parentNode;
          this.init({
            grid,
            // A .b-grid-row will have a data-index
            // If it' a column header, we use rowIndex -1
            rowIndex: grid.store.includes(dataset.id) ? grid.store.indexOf(dataset.id) : dataset.index || -1,
            columnId: cell.dataset.columnId
          });
          this.initialTarget = location2;
        }
      } else {
        this.init(location2);
      }
    }
  }
  init(config) {
    var _a5, _b2;
    const me = this;
    const grid = me.grid = config.grid, { store, columns } = grid, { visibleColumns } = columns;
    if (config.target) {
      me.actionTargets = [me._target = config.target];
    }
    if (config.record) {
      me._id = config.record.isRow ? config.record.id : store.getById(config.record).id;
    } else if ("id" in config) {
      me._id = config.id;
      if (config.id == null) {
        me._rowIndex = -1;
      }
    } else {
      const rowIndex = !isNaN(config.row) ? config.row : !isNaN(config.rowIndex) ? config.rowIndex : NaN;
      me._rowIndex = Math.max(Math.min(Number(rowIndex), store.count - 1), grid.hideHeaders ? 0 : -1);
      me._id = (_a5 = store.records[me._rowIndex]) == null ? void 0 : _a5.id;
    }
    if (!("_rowIndex" in me)) {
      me._rowIndex = store.indexOf(me.id);
    }
    me.isSpecialRow = (_b2 = me.record) == null ? void 0 : _b2.isSpecialRow;
    if ("columnId" in config) {
      me._column = columns.getById(config.columnId);
    } else if ("field" in config) {
      me._column = columns.get(config.field);
    } else {
      const columnIndex = !isNaN(config.column) ? config.column : !isNaN(config.columnIndex) ? config.columnIndex : NaN;
      if (!isNaN(columnIndex)) {
        me._columnIndex = Math.min(Number(columnIndex), visibleColumns.length - 1);
        me._column = visibleColumns[me._columnIndex];
      } else {
        me._column = "column" in config ? isNaN(config.column) ? config.column : visibleColumns[config.column] : visibleColumns[0];
      }
    }
    if (!("_columnIndex" in me)) {
      me._columnIndex = visibleColumns.indexOf(me._column);
    }
  }
  // Class identity indicator. Usually added by extending Base, but we don't do that for perf.
  get isLocation() {
    return true;
  }
  equals(other, shallow = false) {
    const me = this;
    return (other == null ? void 0 : other.isLocation) && other.grid === me.grid && // For a more performant check, use the shallow param
    (shallow ? me.id === other.id && me._column === other._column : other.record === me.record && other.column === me.column && other.target === me.target);
  }
  /**
   * Yields the row index of this location.
   * @property {Number}
   * @readonly
   */
  get rowIndex() {
    const { _id } = this, { store } = this.grid;
    return store.includes(_id) ? store.indexOf(_id) : Math.min(this._rowIndex, store.count - 1);
  }
  /**
   * Used by GridNavigation.
   * @private
   */
  get visibleRowIndex() {
    const { rowManager } = this.grid, { rowIndex } = this;
    return rowIndex === -1 ? rowIndex : Math.max(Math.min(rowIndex, rowManager.lastFullyVisibleTow.dataIndex), rowManager.firstFullyVisibleTow.dataIndex);
  }
  /**
   * Yields `true` if the cell and row are selectable.
   *
   * That is if the record is present in the grid's store and it's not a group summary or group header record.
   * @property {Boolean}
   * @readonly
   */
  get isSelectable() {
    return this.grid.store.includes(this._id) && !this.isSpecialRow;
  }
  get record() {
    var _a5;
    if ((_a5 = this._record) == null ? void 0 : _a5.isModel) {
      return this._record;
    }
    if (this._rowIndex > -1) {
      const { store } = this.grid;
      if (!store.includes(this._id)) {
        return store.records[this._rowIndex];
      }
      return store.getById(this._id);
    }
  }
  get id() {
    return this._id;
  }
  get column() {
    const { visibleColumns } = this.grid.columns;
    if (!(visibleColumns == null ? void 0 : visibleColumns.includes(this._column))) {
      return visibleColumns == null ? void 0 : visibleColumns[this.columnIndex];
    }
    return this._column;
  }
  get columnId() {
    var _a5;
    return (_a5 = this.column) == null ? void 0 : _a5.id;
  }
  /**
   * Yields the column index of this location.
   * @property {Number}
   * @readonly
   */
  get columnIndex() {
    var _a5;
    return Math.min(this._columnIndex, ((_a5 = this.grid.columns.visibleColumns) == null ? void 0 : _a5.length) - 1);
  }
  /**
   * Returns a __*new *__ `GridLocation` instance having moved from the current location in the
   * mode specified.
   * @param {Number} where Where to move from this `GridLocation`. May be:
   *
   *  - `GridLocation.UP`
   *  - `GridLocation.NEXT_CELL`
   *  - `GridLocation.DOWN`
   *  - `GridLocation.PREV_CELL`
   *  - `GridLocation.FIRST_COLUMN`
   *  - `GridLocation.LAST_COLUMN`
   *  - `GridLocation.FIRST_CELL`
   *  - `GridLocation.LAST_CELL`
   *  - `GridLocation.PREV_PAGE`
   *  - `GridLocation.NEXT_PAGE`
   * @returns {Grid.util.GridLocation} A GridLocation object encapsulating the target location.
   */
  move(where) {
    const me = this, {
      record,
      column,
      grid
    } = me, { store } = grid, columns = grid.columns.visibleColumns, result = new _GridLocation();
    let rowIndex = store.includes(record) ? store.indexOf(record) : me.rowIndex, columnIndex = columns.includes(column) ? columns.indexOf(column) : me.columnIndex;
    const rowMin = grid.hideHeaders ? 0 : -1, rowMax = store.count - 1, colMax = columns.length - 1, atFirstRow = rowIndex === rowMin, atLastRow = rowIndex === rowMax, atFirstColumn = columnIndex === 0, atLastColumn = columnIndex === colMax;
    switch (where) {
      case _GridLocation.PREV_CELL:
        if (atFirstColumn) {
          if (!atFirstRow) {
            columnIndex = colMax;
            rowIndex--;
          }
        } else {
          columnIndex--;
        }
        break;
      case _GridLocation.NEXT_CELL:
        if (atLastColumn) {
          if (!atLastRow) {
            columnIndex = 0;
            rowIndex++;
          }
        } else {
          columnIndex++;
        }
        break;
      case _GridLocation.UP:
        if (!atFirstRow) {
          rowIndex--;
        }
        break;
      case _GridLocation.DOWN:
        if (!atLastRow) {
          if (rowIndex === -1) {
            rowIndex = grid.rowManager.firstFullyVisibleRow.dataIndex;
          } else {
            rowIndex++;
          }
        }
        break;
      case _GridLocation.FIRST_COLUMN:
        columnIndex = 0;
        break;
      case _GridLocation.LAST_COLUMN:
        columnIndex = colMax;
        break;
      case _GridLocation.FIRST_CELL:
        rowIndex = rowMin;
        columnIndex = 0;
        break;
      case _GridLocation.LAST_CELL:
        rowIndex = rowMax;
        columnIndex = colMax;
        break;
      case _GridLocation.PREV_PAGE:
        rowIndex = Math.max(rowMin, rowIndex - Math.floor(grid.scrollable.clientHeight / grid.rowHeight));
        break;
      case _GridLocation.NEXT_PAGE:
        rowIndex = Math.min(rowMax, rowIndex + Math.floor(grid.scrollable.clientHeight / grid.rowHeight));
        break;
    }
    result.init({
      grid,
      rowIndex,
      columnIndex
    });
    return result;
  }
  /**
   * The cell DOM element which this `GridLocation` references.
   * @property {HTMLElement}
   * @readonly
   */
  get cell() {
    var _a5, _b2;
    const me = this, {
      grid,
      id: id2,
      _cell
    } = me;
    if (_cell) {
      return _cell;
    }
    if (id2 == null) {
      return (_a5 = grid.columns.getById(me.columnId)) == null ? void 0 : _a5.element;
    } else {
      const { row } = me;
      if (row) {
        return row.getCell(me.columnId) || row.getCell((_b2 = grid.columns.getAt(me.columnIndex)) == null ? void 0 : _b2.id);
      }
    }
  }
  // For debugging purposes only
  get $cellIndex() {
    return { rowIndex: this.rowIndex, columnIndex: this.columnIndex };
  }
  get row() {
    return this.grid.getRowById(this.id) || this.grid.getRow(this.rowIndex);
  }
  /**
   * The DOM element which encapsulates the focusable target of this `GridLocation`.
   *
   * This is usually the {@link #property-cell}, but if this is an actionable location, this
   * may be another DOM element within the cell.
   * @property {HTMLElement}
   * @readonly
   */
  get target() {
    const { cell, _target } = this, { focusableFinder } = this.grid;
    if (cell) {
      if (_target) {
        return _target;
      }
      focusableFinder.currentNode = this.grid.focusableFinderCell = cell;
      return focusableFinder.nextNode() || cell;
    }
  }
  /**
   * This property is `true` if the focus is inside the cell, not *on* the cell.
   * @property {Boolean}
   * @readonly
   */
  get containsFocus() {
    const { cell } = this, activeEl = cell && DomHelper.getActiveElement(cell);
    return activeEl && cell.compareDocumentPosition(activeEl) & Node.DOCUMENT_POSITION_CONTAINED_BY;
  }
  /**
   * This property is `true` if the focus target is not the cell itself.
   * @property {Boolean}
   * @readonly
   */
  get isActionable() {
    const { _target } = this;
    return Boolean(this.containsFocus || _target && _target !== this.cell);
  }
  /**
   * This property is `true` if this location represents a column header.
   * @property {Boolean}
   * @readonly
   */
  get isColumnHeader() {
    return this.cell && this.rowIndex === -1;
  }
  /**
   * This property is `true` if this location represents a cell in the grid body.
   * @property {Boolean}
   * @readonly
   */
  get isCell() {
    return this.cell && this.record;
  }
};
__publicField(_GridLocation, "UP", 1);
__publicField(_GridLocation, "NEXT_CELL", 2);
__publicField(_GridLocation, "DOWN", 3);
__publicField(_GridLocation, "PREV_CELL", 4);
__publicField(_GridLocation, "FIRST_COLUMN", 5);
__publicField(_GridLocation, "LAST_COLUMN", 6);
__publicField(_GridLocation, "FIRST_CELL", 7);
__publicField(_GridLocation, "LAST_CELL", 8);
__publicField(_GridLocation, "PREV_PAGE", 9);
__publicField(_GridLocation, "NEXT_PAGE", 10);
var GridLocation = _GridLocation;
GridLocation._$name = "GridLocation";

// ../Grid/lib/Grid/column/Column.js
var validWidth = (value2) => typeof value2 === "number" || (value2 == null ? void 0 : value2.endsWith("px"));
var arrowFnRegex = /=>\s*['"]/;
var _Column = class _Column extends Model.mixin(Events_default, Localizable_default) {
  /**
   * Column name alias, which you can use in the `columns` array of a Grid. See {@link #property-type-static}
   * @member {String}
   * @readonly
   */
  get type() {
    return this.constructor.type;
  }
  // https://github.com/bryntum/support/issues/10591
  // Add declared `defaults` as `defaultValue` to field definitions
  static setupFields(cls, meta) {
    Model.setupFields(cls, meta);
    const fieldsInfo = meta.fields, fieldMap = fieldsInfo.map;
    fieldsInfo.defs.forEach((def, index) => {
      if (def.defaultValue && fieldsInfo.defaults[def.name] !== def.defaultValue && // Make sure to not overwrite string values that have been already translated
      typeof def.defaultValue === "string" && def.defaultValue.match(/^L\{/)) {
        const newDef = {
          ...def,
          defaultValue: fieldsInfo.defaults[def.name],
          definedBy: this,
          model: this,
          owner: this
        };
        fieldsInfo.defs[index] = newDef;
        fieldMap[def.name] = newDef;
      }
    });
  }
  //endregion
  //region Init
  construct(data, store, meta, ...args) {
    const me = this, isRoot = meta == null ? void 0 : meta.isRoot;
    if (!isRoot) {
      me.masterStore = store;
      if (store) {
        me._grid = Array.isArray(store) ? store[0].grid : store.grid;
      }
      me.localizableProperties = Config.mergeMethods.distinct(data.localizableProperties, ["text", "ariaLabel", "cellAriaLabel"]);
      if (data.localeClass) {
        me.localeClass = data.localeClass;
      }
    }
    super.construct(data, store, meta, ...args);
    const { grid } = me;
    if (!isRoot) {
      if (me.isLeaf && !("field" in me.data)) {
        me.setData("field", "_" + (me.type || "") + ++_Column.emptyCount);
        me.noFieldSpecified = true;
      }
      if (!me.width && !me.flex && !me.children) {
        me.setData({
          width: _Column.defaultWidth,
          flex: null
        }, null, true);
      }
      me.headerWidgets && me.initHeaderWidgets(me.headerWidgets);
      if (me.isParent) {
        me.meta.visibleChildren = /* @__PURE__ */ new Set();
        if (me.collapsible) {
          grid.once("paint", () => me.collapsible = true);
        }
      }
      if (me.renderer) {
        const rendererString = me.renderer.toString();
        me.rendererReturningContent = rendererString.includes("return ") || Boolean(rendererString.match(arrowFnRegex));
      }
      if (me.formula && (!me.field || !grid.store.modelClass.getFieldDefinition(me.field))) {
        console.warn(`No Model field found for this Column\xB4s field: "${me.field}".`);
      }
    }
  }
  /**
   * Checks whether the other column is in the same position and configured the same as this Column.
   * @param {Grid.column.Column} other The partner column to check
   * @returns {Boolean} `true` if these two Columns should be kept in sync.
   * @private
   */
  shouldSync(other) {
    return other.isColumn && other.text === this.text && (other.field === this.field || String(other.renderer) === String(this.renderer)) && (!other.previousSibling && !this.previousSibling || other.previousSibling.shouldSync(this.previousSibling));
  }
  get isCollapsible() {
    var _a5;
    return ((_a5 = this.children) == null ? void 0 : _a5.length) > 1 && this.collapsible;
  }
  get collapsed() {
    return this.get("collapsed");
  }
  set collapsed(collapsed) {
    this.set("collapsed", collapsed, true);
    this.onCollapseChange(!collapsed);
    this.grid.trigger("columnCollapseToggle", { column: this, collapsed });
  }
  onCellFocus(location2) {
    const me = this;
    me.location = location2;
    if (location2.rowIndex !== -1) {
      me.updateCellAriaLabel(me.localizeProperty("cellAriaLabel"));
    }
  }
  updateHeaderAriaLabel(headerAriaLabel) {
    DomHelper.setAttributes(this.element, {
      "aria-label": headerAriaLabel
    });
  }
  updateCellAriaLabel(cellAriaLabel) {
    var _a5, _b2;
    if (!((_a5 = this.location) == null ? void 0 : _a5.isSpecialRow) && ((_b2 = this.location) == null ? void 0 : _b2.cell)) {
      if (!(cellAriaLabel == null ? void 0 : cellAriaLabel.length)) {
        cellAriaLabel = this.location.column.text;
      }
      DomHelper.setAttributes(this.location.cell, {
        "aria-label": cellAriaLabel
      });
    }
  }
  doDestroy() {
    var _a5, _b2, _c2;
    (_c2 = (_b2 = (_a5 = this.data) == null ? void 0 : _a5.editor) == null ? void 0 : _b2.destroy) == null ? void 0 : _c2.call(_b2);
    this.destroyHeaderWidgets();
    super.doDestroy();
  }
  //endregion
  //region Header widgets
  set headerWidgets(widgets) {
    this.initHeaderWidgets(widgets);
    this.set("headerWidgets", widgets);
  }
  get headerWidgets() {
    return this.get("headerWidgets");
  }
  /**
   * An object which contains a map of the widgets contained in this column header keyed by their
   * {@link Core.widget.Widget#config-ref}.
   *
   * @member {Object<String,Core.widget.Widget>} headerWidgetMap
   * @readonly
   * @internal
   */
  initHeaderWidgets(widgets) {
    this.destroyHeaderWidgets();
    const headerWidgetMap = this.headerWidgetMap = {};
    for (const config of widgets) {
      const widget = Widget.create({ owner: this, ...config });
      widget.element.classList.add("b-grid-headerwidget");
      headerWidgetMap[widget.ref || widget.id] = widget;
    }
  }
  destroyHeaderWidgets() {
    var _a5;
    for (const widget of Object.values(this.headerWidgetMap || {})) {
      (_a5 = widget.destroy) == null ? void 0 : _a5.call(widget);
    }
  }
  //endregion
  //region Fields
  // Yields the automatic cell tagging class, e.g. b-number-cell from NumberColumn etc
  static generateAutoCls() {
    const classes = [];
    for (let c = this; c !== _Column; c = c.superclass) {
      c.type && c.type !== c.superclass.type && classes.push(`b-${c.type.toLowerCase()}-cell`);
    }
    const columnAutoCls = classes.join(" ");
    (_Column.autoClsMap || (_Column.autoClsMap = /* @__PURE__ */ new Map())).set(this, columnAutoCls);
    return columnAutoCls;
  }
  /**
   * Determines if the column cell should be marked as `dirty`.
   *
   * @param {Core.data.Model} record The data record
   * @returns {Boolean} Returns `true` if the cell should be marked as dirty, `false` otherwise
   * @internal
   */
  isCellDirty(record) {
    var _a5;
    const { grid, field } = this, { showDirty } = grid, modelField = record.fieldMap[field];
    return showDirty && field && // The field is not a calculated one
    !(modelField == null ? void 0 : modelField.calculated) && // The field is modified
    (record.isFieldModified(field) || // It is a new record (and we should mark dirty cells dirty for it)
    (showDirty == null ? void 0 : showDirty.newRecord) && ((_a5 = grid.store.added) == null ? void 0 : _a5.includes(record))) && // The field is composite or it is being persisted
    (this.compositeField || (modelField == null ? void 0 : modelField.persist) !== false);
  }
  /**
   * Returns the full CSS class set for a cell at the passed {@link Grid.util.GridLocation}
   * as an object where property keys with truthy values denote a class to be added
   * to the cell.
   * @param {Grid.util.GridLocation} cellContext
   * @returns {Object} An object in which property keys with truthy values are used as
   * the class names on the cell element.
   * @internal
   */
  getCellClass(cellContext) {
    var _a5;
    const { record, column } = cellContext, {
      cellCls,
      internalCellCls,
      grid,
      constructor,
      align
    } = column, autoCls = ((_a5 = _Column.autoClsMap) == null ? void 0 : _a5.get(constructor)) || constructor.generateAutoCls(), isEditing = cellContext.cell.classList.contains("b-editing"), result = {
      [grid.cellCls]: grid.cellCls,
      [autoCls]: autoCls,
      [cellCls]: cellCls,
      [internalCellCls]: internalCellCls,
      "b-cell-dirty": column.isCellDirty(record),
      [`b-grid-cell-align-${align}`]: align,
      "b-selected": grid.selectionMode.cell && grid.isCellSelected(cellContext),
      "b-focused": grid.isFocused(cellContext),
      "b-auto-height": column.autoHeight,
      "b-editing": isEditing
    };
    if (record.isSpecialRow && result["b-checkbox-selection"]) {
      result["b-checkbox-selection"] = false;
    }
    return result;
  }
  get locked() {
    return this.data.region === "locked";
  }
  set locked(locked) {
    this.region = locked ? "locked" : "normal";
  }
  // Children of grouped header always uses same region as the group
  get region() {
    if (!this.parent.isRoot) {
      return this.parent.region;
    }
    return this.get("region");
  }
  set region(region) {
    this.set("region", region);
  }
  // column can only be sorted if a `field` is provided, or a custom sort function is provided as `sortable`
  get sortable() {
    const { sortable } = this.data;
    if (!sortable || (this.noFieldSpecified || !this.field) && !sortable.sortFn) {
      return false;
    }
    return sortable;
  }
  set sortable(sortable) {
    this.set("sortable", sortable);
  }
  // column can only be grouped if a `field` is provided
  get groupable() {
    return Boolean(!(this.noFieldSpecified || !this.field) && this.data.groupable);
  }
  set groupable(groupable) {
    this.set("groupable", groupable);
  }
  get filterable() {
    return this.isLeaf && this.data.filterable;
  }
  /**
   * The Field to use as editor for this column
   * @private
   * @readonly
   */
  get editor() {
    const me = this, { grid, field } = me;
    let { editor } = me.data;
    if (editor && !editor.isWidget) {
      const result = grid.processCellEditor({ editor, field: me.field });
      if (result) {
        editor = result.editor;
      } else {
        if (typeof editor === "string") {
          editor = {
            type: editor
          };
        }
        editor = Widget.create(ObjectHelper.merge(me.defaultEditor, {
          owner: grid,
          dataField: grid.store.modelClass.getFieldDefinition(field),
          // Field labels must be present for A11Y purposes, but are clipped out of visibility.
          // Screen readers will be able to access them and announce them.
          label: StringHelper.encodeHtml(me.text)
        }, editor));
        editor.formulaStore = grid.store;
        ArrayHelper.remove(editor.localizableProperties, "label");
      }
      !editor.dontCache && (me.data.editor = editor);
    }
    return editor;
  }
  set editor(editor) {
    this.data.editor = editor;
  }
  afterChange() {
    var _a5;
    super.afterChange(...arguments);
    if ((_a5 = this.data.editor) == null ? void 0 : _a5.isWidget) {
      const { editor } = this, newLabel = StringHelper.encodeHtml(this.text);
      if (editor.label !== newLabel) {
        editor.label = newLabel;
      }
    }
  }
  /**
   * A config object specifying the editor to use to edit this column.
   * @private
   * @readonly
   */
  get defaultEditor() {
    return {
      type: "textfield",
      name: this.field
    };
  }
  //endregion
  //region Grid, SubGrid & Element
  /**
   * Extracts the value from the record specified by this Column's {@link #config-field} specification
   * in a format that can be used as a value to match by a {@link Grid.feature.Filter filtering} operation.
   *
   * The default implementation returns the {@link #function-getRawValue} value, but this may be
   * overridden in subclasses.
   * @param {Core.data.Model} record The record from which to extract the field value.
   * @returns {*} The value of the referenced field if any.
   */
  getFilterableValue(record) {
    return this.getRawValue(record);
  }
  // Create an ownership hierarchy which links columns up to their SubGrid if no owner injected.
  get owner() {
    return this._owner || this.subGrid;
  }
  set owner(owner) {
    this._owner = owner;
  }
  /**
   * Get the Grid instance to which this column belongs
   * @property {Grid.view.Grid}
   * @readonly
   */
  get grid() {
    var _a5;
    return this._grid || ((_a5 = this.parent) == null ? void 0 : _a5.grid);
  }
  // Private, only used in tests where standalone Headers are created with no grid
  // from which to lookup the associate SubGrid.
  set subGrid(subGrid) {
    this._subGrid = subGrid;
  }
  /**
   * Get the SubGrid to which this column belongs
   * @property {Grid.view.SubGrid}
   * @readonly
   */
  get subGrid() {
    var _a5;
    return this._subGrid || ((_a5 = this.grid) == null ? void 0 : _a5.getSubGridFromColumn(this));
  }
  /**
   * Get the element for the SubGrid to which this column belongs
   * @property {HTMLElement}
   * @readonly
   * @private
   */
  get subGridElement() {
    return this.subGrid.element;
  }
  /**
   * The header element for this Column. *Only available after the grid has been rendered*.
   *
   * **Note that column headers are rerendered upon mutation of Column values, so this
   * value is volatile and should not be cached, but should be read whenever needed.**
   * @property {HTMLElement}
   * @readonly
   */
  get element() {
    return this.grid.getHeaderElement(this);
  }
  get previousVisibleSibling() {
    const region = this.region;
    let prev = this.previousSibling;
    while (prev && (prev.hidden || prev.region !== region)) {
      prev = prev.previousSibling;
    }
    return prev;
  }
  get nextVisibleSibling() {
    const region = this.region;
    let next = this.nextSibling;
    while (next && (next.hidden || next.region !== region)) {
      next = next.nextSibling;
    }
    return next;
  }
  get isLastInSubGrid() {
    return !this.nextVisibleSibling && (this.parent.isRoot || this.parent.isLastInSubGrid);
  }
  get allowDrag() {
    return !this.parent.isRoot || Boolean(this.nextVisibleSibling || this.previousVisibleSibling);
  }
  /**
   * The text wrapping element for this Column. *Only available after the grid has been rendered*.
   *
   * This is the full-width element which *contains* the text-bearing element and any icons.
   *
   * **Note that column headers are rerendered upon mutation of Column values, so this
   * value is volatile and should not be cached, but should be read whenever needed.**
   * @property {HTMLElement}
   * @readonly
   */
  get textWrapper() {
    return DomHelper.getChild(this.element, ".b-grid-header-text");
  }
  /**
   * The text containing element for this Column. *Only available after the grid has been rendered*.
   *
   * **Note that column headers are rerendered upon mutation of Column values, so this
   * value is volatile and should not be cached, but should be read whenever needed.**
   * @property {HTMLElement}
   * @readonly
   */
  get textElement() {
    return DomHelper.down(this.element, ".b-grid-header-text-content");
  }
  /**
   * The child element into which content should be placed. This means where any
   * contained widgets such as filter input fields should be rendered. *Only available after the grid has been
   * rendered*.
   *
   * **Note that column headers are rerendered upon mutation of Column values, so this
   * value is volatile and should not be cached, but should be read whenever needed.**
   * @property {HTMLElement}
   * @readonly
   */
  get contentElement() {
    return DomHelper.down(this.element, ".b-grid-header-children");
  }
  //endregion
  //region Misc properties
  get isSorted() {
    var _a5;
    return this.grid.store.sorters.some((s) => s.field === this.field) || ((_a5 = this.grid.store.groupers) == null ? void 0 : _a5.some((g) => g.field === this.field));
  }
  get isFocusable() {
    var _a5;
    return this.isLeaf && !((_a5 = this.grid) == null ? void 0 : _a5.hideHeaders);
  }
  static get text() {
    return this.$meta.fields.defaults.text;
  }
  /**
   * Returns header text based on {@link #config-htmlEncodeHeaderText} config value.
   * @returns {String}
   * @internal
   */
  get headerText() {
    return this.htmlEncodeHeaderText ? StringHelper.encodeHtml(this.text) : this.text;
  }
  /**
   * An object which contains a map of the header widgets keyed by their {@link Core.widget.Widget#config-ref ref}.
   * @property {Object<String,Core.widget.Widget>} headerWidgetMap
   * @private
   * @readonly
   */
  //endregion
  //region Show/hide
  get isVisible() {
    return !this.hidden && (!this.parent || this.parent.isVisible);
  }
  /**
   * Hides this column.
   */
  hide(silent = false, hidingParent = false) {
    const me = this, { parent } = me;
    if (!me.hidden) {
      me.hidden = true;
      if (parent && !parent.isRoot && !parent.isTogglingAll) {
        const anyVisible = parent.children.some((child) => child.hidden !== true);
        if (!anyVisible && !parent.hidden) {
          silent = true;
          parent.hide();
        }
      }
      if (me.isParent) {
        me.children.forEach((child) => child.hide(true, true));
      } else if (!parent.isRoot) {
        parent.meta.visibleChildren[hidingParent ? "add" : "delete"](me);
      }
      if (!silent) {
        me.stores.forEach((store) => store.trigger("columnHide", { column: me }));
      }
    }
  }
  /**
   * Shows this column.
   */
  show(silent = false) {
    var _a5;
    const me = this, { parent } = me;
    if (me.hidden) {
      me.hidden = false;
      if (parent == null ? void 0 : parent.hidden) {
        parent.show();
      }
      if (me.isParent) {
        (_a5 = me.meta.visibleChildren) == null ? void 0 : _a5.forEach((child) => child.show(true));
      }
      if (!silent) {
        me.stores.forEach((store) => store.trigger("columnShow", { column: me }));
      }
    }
  }
  /**
   * Toggles the column visibility.
   * @param {Boolean} [force] Set to `true` (visible) or `false` (hidden) to force a certain state
   */
  toggle(forceVisible) {
    if (this.hidden && forceVisible === void 0 || forceVisible === true) {
      return this.show();
    }
    if (!this.hidden && forceVisible === void 0 || forceVisible === false) {
      return this.hide();
    }
  }
  /**
   * Toggles the column visibility of all children of a parent column.
   * @param {Grid.column.Column[]} [columns] The set of child columns to toggle, defaults to all children
   * @param {Boolean} [force] Set to `true` (visible) or `false` (hidden) to force a certain state
   */
  toggleChildren(columns = this.children, force = void 0) {
    var _a5, _b2;
    const me = this;
    (_a5 = me.grid.columns) == null ? void 0 : _a5.beginBatch();
    me.isTogglingAll = true;
    columns.forEach((childColumn) => childColumn.toggle(force));
    me.isTogglingAll = false;
    (_b2 = me.grid.columns) == null ? void 0 : _b2.endBatch();
  }
  /**
   * Toggles the collapsed state of the column. Based on the {@link #config-collapseMode}, this either hides all
   * but the first child column, or toggles the visibility state of all children (if you want to have a special
   * column shown in collapsed mode).
   *
   * Only applicable for columns with child columns.
   * @private
   * @param {Boolean} [force] Set to `true` (expanded) or `false` (collapsed) to force a certain state
   */
  onCollapseChange(force = void 0) {
    var _a5, _b2;
    const me = this;
    if (me.collapseMode === "toggleAll") {
      me.toggleChildren();
    } else {
      const { firstChild } = me;
      if (firstChild.flex != null && me.collapsed) {
        firstChild.oldFlex = firstChild.flex;
        firstChild.width = firstChild.element.offsetWidth;
      } else if (!me.collapsed && firstChild.oldFlex) {
        firstChild.flex = firstChild.oldFlex;
        firstChild.oldFlex = null;
      }
      (_a5 = me.grid.columns) == null ? void 0 : _a5.beginBatch();
      me.isTogglingAll = true;
      me.children.slice(1).forEach((childColumn) => childColumn.toggle(force));
      me.isTogglingAll = false;
      (_b2 = me.grid.columns) == null ? void 0 : _b2.endBatch();
    }
  }
  set collapsible(collapsible) {
    const me = this;
    me.set("collapsible", collapsible);
    if (me.isParent) {
      const { headerWidgets = [] } = me;
      if (collapsible) {
        headerWidgets.push({
          type: "button",
          ref: "collapseExpand",
          toggleable: true,
          pressed: me.collapsed,
          icon: `b-icon-collapse-${me.grid.rtl ? "right" : "left"}`,
          pressedIcon: `b-icon-collapse-${me.grid.rtl ? "left" : "right"}`,
          cls: "b-grid-header-collapse-button b-transparent",
          onToggle: ({ pressed }) => me.collapsed = pressed
        });
      } else {
        const index = headerWidgets.findIndex((w) => w.ref === "collapseExpand");
        index > -1 && headerWidgets.splice(index, 1);
      }
      me.headerWidgets = headerWidgets;
      if (me.collapsed) {
        me.onCollapseChange(false);
      }
    }
  }
  get collapsible() {
    return this.get("collapsible");
  }
  //endregion
  //region Index & id
  /**
   * Generates an id for the column when none is set. Generated ids are 'col1', 'col2' and so on. If a field is
   * specified (as it should be in most cases) the field name is used instead: 'name1', 'age2' ...
   * @private
   * @returns {String}
   */
  generateId() {
    var _a5, _b2, _c2;
    const { grid } = this, parts = [(_b2 = (_a5 = this.field) == null ? void 0 : _a5.replace(/\./g, "-")) != null ? _b2 : "col"];
    if (grid) {
      parts.unshift((_c2 = grid.stateId) != null ? _c2 : grid.id);
      if (!grid.generatedIdIndex) {
        grid.generatedIdIndex = 0;
      }
      parts.push(grid.generatedIdIndex += 1);
    } else {
      if (!_Column.generatedIdIndex) {
        _Column.generatedIdIndex = 0;
      }
      parts.push(_Column.generatedIdIndex += 1);
    }
    return this.meta.generatedId = parts.join("-");
  }
  /**
   * Index among all flattened columns
   * @property {Number}
   * @readOnly
   * @internal
   */
  get allIndex() {
    return this.masterStore.indexOf(this);
  }
  //endregion
  //region Width
  // Returns size in pixels for measured value
  measureSize(value2) {
    var _a5;
    return DomHelper.measureSize(value2, (_a5 = this.subGrid) == null ? void 0 : _a5.element);
  }
  /**
   * Returns minimal width in pixels for applying to style according to the current `width` and `minWidth`.
   * @internal
   */
  get calcMinWidth() {
    const { width, minWidth } = this.data;
    if (validWidth(width) && validWidth(minWidth)) {
      return Math.max(parseInt(width) || 0, parseInt(minWidth) || 0);
    } else {
      return width;
    }
  }
  /**
   * Get/set columns width in px. If column uses flex, width will be undefined.
   * Setting a width on a flex column cancels out flex.
   *
   * **NOTE:** Grid might be configured to always stretch the last column, in which case the columns actual width
   * might deviate from the configured width.
   *
   * ```javascript
   * let grid = new Grid({
   *     appendTo : 'container',
   *     height   : 200,
   *     width    : 400,
   *     columns  : [{
   *         text  : 'First column',
   *         width : 100
   *     }, {
   *         text  : 'Last column',
   *         width : 100 // last column in the grid is always stretched to fill the free space
   *     }]
   * });
   *
   * grid.columns.last.element.offsetWidth; // 300 -> this points to the real element width
   * ```
   * @property {Number|String}
   */
  get width() {
    return this.data.width;
  }
  set width(width) {
    const data = { width };
    if (width && "flex" in this.data) {
      data.flex = null;
    }
    this.set(data);
  }
  set flex(flex) {
    const data = { flex };
    if (flex && "width" in this.data) {
      data.width = null;
    }
    this.set(data);
  }
  get flex() {
    return this.data.flex;
  }
  // This method is used to calculate minimum row width for edge and safari
  // It calculates minimum width of the row taking column hierarchy into account
  calculateMinWidth() {
    const me = this, width = me.measureSize(me.width), minWidth = me.measureSize(me.minWidth);
    let minChildWidth = 0;
    if (me.children) {
      minChildWidth = me.children.reduce((result, column) => {
        return result + column.calculateMinWidth();
      }, 0);
    }
    return Math.max(width, minWidth, minChildWidth);
  }
  /**
   * Resizes the column to match the widest string in it. By default it also measures the column header, this
   * behaviour can be configured by setting {@link Grid.view.Grid#config-resizeToFitIncludesHeader}.
   *
   * Called internally when you double-click the edge between column headers, but can also be called programmatically.
   * For performance reasons it is limited to checking 1000 rows surrounding the current viewport.
   *
   * @param {Number|Number[]} widthMin Minimum allowed width. If content width is less than this, this width is used
   * instead. If this parameter is an array, the first element is `widthMin` and the seconds is `widthMax`.
   * @param {Number} widthMax Maximum allowed width. If the content width is greater than this number, this width
   * is used instead.
   */
  resizeToFitContent(widthMin, widthMax, batch = false) {
    var _a5, _b2;
    const me = this, {
      grid,
      element,
      fitMode
    } = me, { rowManager } = grid, { records } = grid.$store, count = records.length;
    if (count <= 0 || me.fitMode === "none" || !me.fitMode || DomHelper.isVueConfig(me.data)) {
      return;
    }
    const [row] = rowManager.rows, {
      rowElement,
      cellElement
    } = grid.beginGridMeasuring(), cellContext = new GridLocation({
      grid,
      column: me,
      id: null
    });
    let maxWidth = 0, start, end, i, record, value2, length, longest = { length: 0, record: null };
    cellElement._domData = {
      columnId: me.id,
      row,
      rowElement
    };
    cellContext._cell = cellElement;
    cellContext.updatingSingleRow = true;
    cellContext.isMeasuring = true;
    cellElement.innerHTML = "";
    if (grid.resizeToFitIncludesHeader && !grid.hideHeaders) {
      if (!grid.$headerPadding) {
        const style = globalThis.getComputedStyle(element);
        grid.$headerPadding = parseInt(style.paddingLeft);
      }
      const headerText = element.querySelector(".b-grid-header-text-content");
      headerText.style.cssText = "flex: none; width: auto";
      maxWidth = headerText.offsetWidth + (grid.cellEllipsis ? 1 : 0) + grid.$headerPadding * 2 + 2;
      headerText.style.cssText = "";
    }
    if (count > 1e3) {
      start = Math.max(Math.min(rowManager.topIndex + Math.round(rowManager.rowCount / 2) - 500, count - 1e3), 0);
      end = start + 1e3;
    } else {
      start = 0;
      end = count;
    }
    for (i = start; i < end; i++) {
      record = records[i];
      value2 = me.getRawValue(record);
      if (fitMode === "value") {
        length = String(value2).length;
      } else {
        cellContext._record = record;
        cellContext._id = record.id;
        cellContext._rowIndex = i;
        row.renderCell(cellContext);
        if (fitMode === "textContent") {
          length = cellElement.textContent.length;
        } else {
          const width = cellElement.offsetWidth + (grid.cellEllipsis ? 1 : 0);
          if (width > maxWidth) {
            maxWidth = width;
          }
        }
      }
      if (length > longest.length) {
        longest = { record, length, rowIndex: i };
      }
    }
    if ((_a5 = grid.features.summary) == null ? void 0 : _a5.enabled) {
      const footerValueEl = (_b2 = grid.getFooterElement(me)) == null ? void 0 : _b2.querySelector(".b-grid-summary-value");
      if (footerValueEl) {
        const width = footerValueEl.scrollWidth + grid.$headerPadding * 2 + 2;
        if (width > maxWidth) {
          maxWidth = width;
        }
      }
    }
    if (longest.length > 0 && (fitMode === "value" || fitMode === "textContent")) {
      cellContext._record = longest.record;
      cellContext._id = longest.record.id;
      cellContext._rowIndex = longest.rowIndex;
      row.renderCell(cellContext);
      maxWidth = Math.max(maxWidth, cellElement.offsetWidth);
    }
    if (Array.isArray(widthMin)) {
      [widthMin, widthMax] = widthMin;
    }
    maxWidth = Math.max(maxWidth, widthMin || 0);
    maxWidth = Math.min(maxWidth, widthMax || 1e6);
    if (!batch) {
      grid.endGridMeasuring();
    }
    me.width = me.maxWidth ? maxWidth = Math.min(maxWidth, me.maxWidth) : maxWidth;
    return maxWidth;
  }
  //endregion
  //region State
  /**
   * Get column state, used by State mixin
   * @private
   */
  getState() {
    const me = this, { parentId } = me, state = {
      id: me.id,
      type: me.type,
      // State should only store column attributes which user can modify via UI (except column index).
      // User can hide column, resize or move it to neighbor region
      hidden: me.hidden,
      region: me.region,
      locked: me.locked,
      text: me.text
    };
    if (!me.noFieldSpecified) {
      state.field = me.field;
    }
    if (parentId != null) {
      state.parentId = parentId;
    }
    if (!me.children) {
      state[me.flex ? "flex" : "width"] = me.flex || me.width;
    }
    if (me.isCollapsible) {
      state.collapsed = me.collapsed;
    }
    return state;
  }
  /**
   * Apply state to column, used by State mixin
   * @private
   */
  applyState(state) {
    const me = this;
    me.beginBatch();
    if ("locked" in state) {
      me.locked = state.locked;
    }
    if ("width" in state) {
      me.width = state.width;
    }
    if ("flex" in state) {
      me.flex = state.flex;
    }
    if ("width" in state && me.flex) {
      me.flex = void 0;
    } else if ("flex" in state && me.width) {
      me.width = void 0;
    }
    if ("region" in state) {
      me.region = state.region;
    }
    if ("text" in state) {
      me.text = state.text;
    }
    me.endBatch();
    if ("hidden" in state) {
      me.toggle(state.hidden !== true);
    }
    if ("collapsed" in state) {
      me.collapsed = state.collapsed;
    }
  }
  //endregion
  //region Other
  remove() {
    const { subGrid, grid } = this, focusedCell = subGrid && (grid == null ? void 0 : grid.focusedCell);
    if ((focusedCell == null ? void 0 : focusedCell.columnId) === this.id) {
      if (grid.owns(DomHelper.getActiveElement(grid))) {
        grid.navigateRight();
      } else {
        grid._focusedCell = new GridLocation({
          grid,
          rowIndex: focusedCell.rowIndex,
          column: subGrid.columns.getAdjacentVisibleLeafColumn(this.id, true, true)
        });
      }
    }
    super.remove();
  }
  /**
   * Extracts the value from the record specified by this Column's {@link #config-field} specification.
   *
   * This will work if the field is a dot-separated path to access fields in associated records, eg
   *
   * ```javascript
   *  field : 'resource.calendar.name'
   * ```
   *
   * **Note:** This is the raw field value, not the value returned by the {@link #config-renderer}.
   * @param {Core.data.Model} record The record from which to extract the field value.
   * @returns {*} The value of the referenced field if any.
   */
  getRawValue(record, editing) {
    return record.getValue(editing ? this.editorField : this.field);
  }
  /**
   * Refresh the cell for supplied record in this column, if that cell is rendered.
   * @param {Core.data.Model} record Record used to get row to update the cell in
   */
  refreshCell(record) {
    this.grid.rowManager.refreshCell(record, this.id);
  }
  /**
   * Refreshes all the cells for this column
   */
  refreshCells() {
    this.grid.refreshColumn(this);
  }
  /**
   * Rerender the header for this column
   */
  refreshHeader() {
    this.grid.refreshHeader(this);
  }
  /**
   * Clear cell contents. Base implementation which just sets innerHTML to blank string.
   * Should be overridden in subclasses to clean up for examples widgets.
   * @param {HTMLElement} cellElement
   * @internal
   */
  clearCell(cellElement) {
    cellElement.innerHTML = "";
    delete cellElement._content;
  }
  /**
   * Override in subclasses to allow/prevent editing of certain rows.
   * @param {Core.data.Model} record
   * @internal
   */
  canEdit(record) {
    return !this.readOnly && this.editor && record.isEditable(this.editorField) !== false;
  }
  /**
   * Insert a child column(s) before an existing child column. Returns `null` if the parent column is
   * {@link #config-sealed}
   * @param {Core.data.Model|Core.data.Model[]} childColumn Column or array of columns to insert
   * @param {Core.data.Model} [before] Optional column to insert before, leave out to append to the end
   * @param {Boolean} [silent] Pass `true` to not trigger events during insert
   * @returns {Core.data.Model|Core.data.Model[]|null}
   * @category Parent & children
   */
  insertChild(childColumn, before = null, silent = false) {
    childColumn = Array.isArray(childColumn) ? childColumn : [childColumn];
    childColumn.forEach((col) => {
      const { parent } = col;
      if ((parent == null ? void 0 : parent.collapsed) && col === parent.firstChild && parent.children.length > 1 && parent.children.filter((child) => !child.hidden).length === 1) {
        col.nextSibling.hidden = false;
      }
    });
    return this.sealed && !this.inProcessChildren ? null : super.insertChild(...arguments);
  }
  /**
   * Override in subclasses to prevent this column from being filled with the {@link Grid.feature.FillHandle} feature
   * @param {Object} data Object containing information about current cell and fill value
   * @param {Grid.util.GridLocation} data.cell Current cell data
   * @param {Grid.util.GridLocation[]} data.range Range from where to calculate values
   * @param {Core.data.Model} data.record Current cell record
   * @param {Boolean} data.enforceType Pass true to verify type of the value can be accepted by this column
   * @param {*} data.value value to be applied
   * @returns {Boolean}
   * @internal
   */
  canFillValue({ range, record, cell, value: value2, enforceType }) {
    var _a5;
    let result = !this.readOnly && record.isEditable(this.field) !== false;
    if (result && enforceType) {
      const dataField = record.getFieldDefinition(this.field), dataConstructor = (_a5 = dataField == null ? void 0 : dataField.constructor) == null ? void 0 : _a5.dataConstructor, matchingTypes = value2 == null || !dataField || (dataField == null ? void 0 : dataField.type) === "auto" || dataConstructor && (value2 instanceof dataConstructor || typeof value2 === dataConstructor.name.toLowerCase());
      result = matchingTypes;
    }
    return result;
  }
  calculateFillValue({ value: value2, record, fromCell, range, horizontal }) {
    const dataField = record.getFieldDefinition(this.field);
    if (horizontal && range.length === 1 && (!dataField || (dataField == null ? void 0 : dataField.type) === "auto" || (dataField == null ? void 0 : dataField.type) === "string")) {
      value2 = range[0].cell.innerText.trim();
    }
    return value2;
  }
  //endregion
  // This function is not meant to be called by any code other than Base#getCurrentConfig().
  // It extracts the current configs (fields) for the column, with special handling for sortable, editor, renderer and
  // headerRenderer
  getCurrentConfig(options) {
    var _a5;
    const result = super.getCurrentConfig(options);
    if ((_a5 = this.sortable) == null ? void 0 : _a5.originalSortFn) {
      result.sortable = this.sortable.originalSortFn;
    }
    if (result.headerRenderer === this.internalHeaderRenderer) {
      delete result.headerRenderer;
    }
    delete result.ariaLabel;
    delete result.cellAriaLabel;
    delete result.disableRendererWarning;
    return result;
  }
  get visible() {
    return this.isVisible;
  }
  /**
   * Set this column to be visible or not
   * @member {Boolean} visible
   */
  set visible(value2) {
    this.hidden = !value2;
  }
  get readOnly() {
    var _a5;
    const { field, grid } = this, readOnly = Boolean(this.get("readOnly"));
    if (field && grid) {
      return ((_a5 = grid.store.modelClass.getFieldDefinition(field)) == null ? void 0 : _a5.calculated) || readOnly;
    }
    return readOnly;
  }
  set readOnly(value2) {
    this.set("readOnly", value2);
  }
  get renderer() {
    var _a5;
    return ((_a5 = this.data) == null ? void 0 : _a5.renderer) || this.defaultRenderer;
  }
  set renderer(renderer) {
    this.set("renderer", renderer);
  }
  get hasSummary() {
    return this.sum || this.summaries;
  }
  /**
   * Yields the name of the record field which the {@link Grid.feature.CellEdit} feature edits when invoked on
   * this column. This is usually this Column's {@link #config-field}, but Column subclasses may implement
   * this property.
   * @property {String}
   * @internal
   */
  get editorField() {
    const { field } = this, dataField = field && this.grid.store.modelClass.getFieldDefinition(field);
    if (dataField == null ? void 0 : dataField.formulaProviders) {
      return `#${field}`;
    }
    return field;
  }
};
__publicField(_Column, "$name", "Column");
/**
 * Column name alias which you can use in the `columns` array of a Grid.
 *
 * ```javascript
 * class MyColumn extends Column {
 *     static get type() {
 *        return 'mycolumn';
 *     }
 * }
 * ```
 *
 * ```javascript
 * const grid = new Grid({
 *    columns : [
 *       { type : 'mycolumn', text : 'The column', field : 'someField', flex : 1 }
 *    ]
 * });
 * ```
 *
 * @static
 * @member {String} type
 * @readonly
 */
__publicField(_Column, "type", "column");
//region Config
/**
 * Default settings for the column, applied in constructor. None by default, override in subclass.
 * @member {Object} defaults
 * @returns {Object}
 * @readonly
 */
__publicField(_Column, "fields", [
  /**
   * @hidefields expanded
   */
  /**
   * @hidefunctions ancestorsExpanded, isExpanded
   */
  //region Common
  "type",
  /**
   * Header text
   * @prp {String} text
   * @category Common
   */
  "text",
  /**
   * The {@link Core.data.field.DataField#config-name} of the {@link Core.data.Model data model} field to
   * read a cells value from.
   *
   * Also accepts dot notation to read nested or related data, for example `'address.city'`.
   *
   * @prp {String} field
   * @readonly
   * @category Common
   */
  "field",
  // NOTE: This is duplicated in WidgetColumn, NumberColumn, DateColumn and partly in TreeColumn so remember to change there too if
  // changing the signature of this function
  /**
   * Renderer function, used to format and style the content displayed in the cell. Return the cell text you
   * want to display. Can also affect other aspects of the cell, such as styling.
   *
   * <div class="note">
   * You should never modify any records inside this method.
   * </div>
   *
   * **NOTE:** If you provide a `renderer` to a built-in Column such as DateColumn, it will completely replace
   * the native rendering.
   *
   * **NOTE:** If you mutate `cellElement`, and you want to prevent cell content from being reset during
   * rendering, please return `undefined` from the renderer (or just omit the `return` statement) and make
   * sure that the {@link #config-alwaysClearCell} config is set to `false`.
   *
   * ```javascript
   * new Grid({
   *     columns : [
   *         { text : 'Status', renderer : ({ record }) => record.status }
   *     ]
   * });
   * ```
   *
   * You can also return a {@link Core.helper.DomHelper#typedef-DomConfig} object describing the markup:
   *
   * ```javascript
   * new Grid({
   *     columns : [
   *         {
   *              text : 'Status',
   *              renderer : ({ record }) => {
   *                  return {
   *                      class : 'myClass',
   *                      children : [
   *                          {
   *                              tag : 'i',
   *                              class : 'fa fa-pen'
   *                          },
   *                          {
   *                              tag : 'span',
   *                              text : record.name
   *                          }
   *                      ]
   *                  };
   *              }
   *         }
   *     ]
   * });
   * ```
   *
   * You can modify the row element too from inside a renderer to add custom CSS classes:
   *
   * ```javascript
   * new Grid({
   *     columns : [
   *         {
   *             text     : 'Name',
   *             renderer : ({ record, row }) => {
   *                // Add special CSS class to new rows that have not yet been saved
   *               row.cls.newRow = record.isPhantom;
   *
   *               return record.name;
   *         }
   *     ]
   * });
   * ```
   *
   * @config {Function} renderer
   * @param {Object} renderData Object containing renderer parameters
   * @param {HTMLElement} renderData.cellElement Cell element, for adding CSS classes, styling etc. Can be `null` in case of export
   * @param {*} renderData.value Value to be displayed in the cell
   * @param {Core.data.Model} renderData.record Record for the row
   * @param {Grid.column.Column} renderData.column This column
   * @param {Grid.view.Grid} renderData.grid This grid
   * @param {Grid.row.Row} renderData.row Row object. Can be null in case of export.
   *   Use the {@link Grid.row.Row#function-assignCls row's API} to manipulate CSS class names.
   * @param {Object} renderData.size Set `size.height` to specify the desired row height for the current
   *   row. Largest specified height is used, falling back to configured {@link Grid/view/Grid#config-rowHeight}
   *   in case none is specified. Can be null in case of export
   * @param {Number} renderData.size.height Set this to request a certain row height
   * @param {Number} renderData.size.configuredHeight Row height that will be used if none is requested
   * @param {Boolean} renderData.isExport `true` if the record is being exported to Excel or a textual format, enabling special handling during export.
   * @param {Boolean} renderData.isMeasuring True if the column is being measured for a `resizeToFitContent` call.
   *   In which case an advanced renderer might need to take different actions.
   * @returns {String|DomConfig|DomConfig[]|null}
   *
   * @category Rendering
   */
  "renderer",
  /**
   * A callback function called after every cell has been rendered, which lets you mutate the cell/row or
   * contents. This is useful when you want to mutate contents of a pre-configured column (e.g. `CheckColumn`,
   * `WidgetColumn` etc.) without overwriting the built-in `renderer`.
   *
   * In the method, you can modify the cell / row elements, e.g. to add custom CSS classes:
   *
   * ```javascript
   * new Grid({
   *     columns : [
   *         {
   *             text     : 'Name',
   *             afterRenderCell : ({ record, row, cellElement }) => {
   *                 cellElement.classList.toggle('myClass', true);
   *                 // Add special CSS class to new rows that have not yet been saved
   *                 row.cls.newRow = record.isPhantom;
   *             }
   *         }
   *     ]
   * });
   * ```
   *
   * @config {Function} afterRenderCell
   * @param {Object} renderData Object containing rendering parameters
   * @param {HTMLElement} renderData.cellElement Cell element, for adding CSS classes, styling etc. Can be `null` in case of export
   * @param {*} renderData.value Value to be displayed in the cell
   * @param {Core.data.Model} renderData.record Record for the row
   * @param {Grid.column.Column} renderData.column This column
   * @param {Grid.view.Grid} renderData.grid This grid
   * @param {Grid.row.Row} renderData.row Row object. Can be null in case of export.
   *   Use the {@link Grid.row.Row#function-assignCls row's API} to manipulate CSS class names.
   * @param {Object} renderData.size Set `size.height` to specify the desired row height for the current
   *   row. Largest specified height is used, falling back to configured {@link Grid/view/Grid#config-rowHeight}
   *   in case none is specified. Can be null in case of export
   * @param {Number} renderData.size.height Set this to request a certain row height
   * @param {Number} renderData.size.configuredHeight Row height that will be used if none is requested
   * @param {Boolean} renderData.isExport `true` if the record is being exported to Excel or a textual format, enabling special handling during export.
   * @param {Boolean} renderData.isMeasuring True if the column is being measured for a `resizeToFitContent` call.
   * @returns {void|String|null} Return value is only used by the export feature, not by row rendering
   *
   * @category Rendering
   */
  "afterRenderCell",
  //'reactiveRenderer',
  /**
   * Column width. If value is Number then width is in pixels
   * @config {Number|String} width
   * @category Common
   */
  "width",
  /**
   * Column width as a flex weight. All columns with flex specified divide the available space (after
   * subtracting fixed widths) between them according to the flex value. Columns that have `flex : 2` will be
   * twice as wide as those with `flex : 1` (and so on)
   * @prp {Number|String} flex
   * @category Common
   */
  "flex",
  /**
   * This config sizes a column to fits its content. It is used instead of `width` or `flex`.
   *
   * This config requires the {@link Grid.feature.ColumnAutoWidth} feature which responds to changes in the
   * grid's store and synchronizes the widths' of all `autoWidth` columns.
   *
   * If this config is not a Boolean value, it is passed as the only argument to the `resizeToFitContent`
   * method to constrain the column's width.
   *
   * @config {Boolean|Number|Number[]} autoWidth
   * @category Common
   * @non-lazy-load
   */
  "autoWidth",
  /**
   * This config enables automatic height for all cells in this column. It is achieved by measuring the height
   * a cell after rendering it to DOM, and then sizing the row using that height (if it is greater than other
   * heights used for the row).
   *
   * Heads up if you render your Grid on page load, if measurement happens before the font you are using is
   * loaded you might get slightly incorrect heights. For browsers that support it we detect that
   * and remeasure when fonts are available.
   *
   * **NOTE:** Enabling this config comes with a pretty big performance hit. To maintain good performance,
   * we recommend not using it. You can still set the height of individual rows manually, either through
   * {@link Grid.data.GridRowModel#field-rowHeight data} or via {@link #config-renderer renderers}.
   *
   * Also note that this setting only works fully as intended with non-flex columns.
   *
   * Rows will always be at least {@link Grid.view.Grid#config-rowHeight} pixels tall
   * even if an autoHeight cell contains no data.
   *
   * Manually setting a height from a {@link #config-renderer} in this column will take precedence over this
   * config.
   *
   * @config {Boolean} autoHeight
   * @category Common
   */
  "autoHeight",
  /**
   * Mode to use when measuring the contents of this column in calls to {@link #function-resizeToFitContent}.
   * Available modes are:
   *
   * * 'exact'       - Most precise, renders and measures all cells (Default, slowest)
   * * 'textContent' - Renders all cells but only measures the one with the longest `textContent`
   * * 'value'       - Renders and measures only the cell with the longest data (Fastest)
   * * 'none'/falsy  - Resize to fit content not allowed, a call does nothing
   *
   * @config {'exact'|'textContent'|'value'|'none'|null} fitMode
   * @default 'exact'
   * @category Common
   */
  { name: "fitMode", defaultValue: "exact" },
  //endregion
  //region Interaction
  /**
   * Set this to `true` to not allow any type of editing in this column.
   * @prp {Boolean} readOnly
   */
  "readOnly",
  /**
   * A config object used to create the input field which will be used for editing cells in the
   * column. Used when {@link Grid.feature.CellEdit} feature is enabled. The Editor refers to
   * {@link #config-field} for a data source.
   *
   * Configure this as `false` or `null` to prevent cell editing in this column.
   *
   * All subclasses of {@link Core.widget.Field} can be used as editors. The most popular are:
   * - {@link Core.widget.TextField}
   * - {@link Core.widget.NumberField}
   * - {@link Core.widget.DateField}
   * - {@link Core.widget.TimeField}
   * - {@link Core.widget.Combo}
   *
   * If record has method set + capitalized field, method will be called, e.g. if record has method named
   * `setFoobar` and the {@link #config-field} is `foobar`, then instead of `record.foobar = value`,
   * `record.setFoobar(value)` will be called.
   *
   * `Function` may be used for React application parameter for using JSX components as editors.
   *
   * ```javascript
   *  columns : [
   *         {
   *             type   : 'name',
   *             field  : 'name',
   *             width  : 250,
   *             editor : ref => <TextEditor ref={ref}/>
   *         },
   *         ...
   * ]
   * ```
   *
   * where passing `ref` to the React editor is essential for the editor to work.
   *
   * NOTE: React editor component must implement `setValue` method which usually internally calls `setState`.
   * React `setState` is asynchronous so we need to return Promise which will be resolved when `setState`
   * finishes. A typical example of `setValue` method implemented in a React editor is:
   *
   * ```javascript
   * setValue(value) {
   *     return new Promise(resolve => this.setState({ value }, () => resolve(value)));
   * }
   * ```
   *
   * Please consult the
   * [React Integration Guide](#Grid/guides/integration/react/guide.md#using-react-as-cell-editor)
   * for details on usage of React cell editors.
   *
   * Note that for Column subclasses which have a default editor, the editor config is merged
   * with the Column class's default editor config. This allows an application to reconfigure
   * the default editor to conform to requirements for a specific column:
   *
   * ```javascript
   * new Grid({
   *     columns : [{
   *         type  : 'date',
   *         field : 'date',
   *         // Override defaults for the date editor
   *         editor : {
   *             step : null // disable back/forward stepping
   *         }
   *     }]
   * });
   * ```
   *
   * @config {Boolean|String|InputFieldConfig|Core.widget.Field|Function|null} editor
   * @param {*} ref React `RefObject` for editor JSX component.
   * @returns {*} Returns React editor JSX component template
   *
   * @category Interaction
   */
  {
    name: "editor",
    defaultValue: {},
    convert(value2, data, column) {
      return ObjectHelper.isObject(value2) ? ObjectHelper.merge({}, column.constructor.defaultValues.editor, value2) : value2;
    }
  },
  /**
   * A config object used to configure an {@link Core.widget.Editor} which contains this Column's
   * {@link #config-editor input field} if {@link Grid.feature.CellEdit} feature is enabled.
   * @config {EditorConfig} cellEditor
   * @category Interaction
   */
  "cellEditor",
  /**
   * A function which is called when a cell edit is requested to finish.
   *
   * This may be an `async` function which performs complex validation. The return value should be:
   * - `false` - To indicate a generic validation error
   * - `true` - To indicate a successful validation, which will complete the editing
   * - a string - To indicate an error message of the failed validation. This error message will be cleared
   * upon any subsequent user input.
   *
   * The action for the failed validation is defined with the {@link #config-invalidAction} config.
   *
   * For example for synchronous validation:
   *
   * ```javascript
   * const grid = new Grid({
   *    columns : [
   *       {
   *          type : 'text',
   *          text : 'The column',
   *          field : 'someField',
   *          flex : 1,
   *          finalizeCellEdit : ({ value }) => {
   *              return value.length < 4 ? 'Value length should be at least 4 characters' : true;
   *          }
   *       }
   *    ]
   * });
   * ```
   * Here we've defined a validation `finalizeCellEdit` function, which marks all edits with new value
   * less than 4 characters length as invalid.
   *
   * For asynchronous validation you can make the validation function async:
   *
   * ```javascript
   * finalizeCellEdit : async ({ value }) => {
   *     return await performRemoteValidation(value);
   * }
   * ```
   *
   * @config {Function} finalizeCellEdit
   * @param {Object} context An object describing the state of the edit at completion request time.
   * @param {Core.widget.Field} context.inputField The field configured as the column's `editor`.
   * @param {Core.data.Model} context.record The record being edited.
   * @param {*} context.oldValue The old value of the cell.
   * @param {*} context.value The new value of the cell.
   * @param {Grid.view.Grid} context.grid The host grid.
   * @param {Object} context.editorContext The {@link Grid.feature.CellEdit} context object.
   * @param {Grid.column.Column} context.editorContext.column The column being edited.
   * @param {Core.data.Model} context.editorContext.record The record being edited.
   * @param {HTMLElement} context.editorContext.cell The cell element hosting the editor.
   * @param {Core.widget.Editor} context.editorContext.editor The floating Editor widget which is hosting the input field.
   * @returns {Boolean|void}
   * @async
   *
   * @category Interaction
   */
  "finalizeCellEdit",
  /**
   * By default, cell editing is finalized when the editor is blurred or if the user taps outside the editor.
   * For complex custom editors, focus or tapping might be expected outside the Bryntum owned editor. In such
   * cases, supply `false` for this config to take manual control over when cell editing in the column should
   * be finalized.
   *
   * To accept changes, call {@link Grid.feature.CellEdit#function-finishEditing}. To reject, call
   * {@link Grid.feature.CellEdit#function-cancelEditing}.
   *
   * ```javascript
   * // Setup
   * const grid = new Grid({
   *   columns : [
   *     {
   *       text               : 'Skills',
   *       field              : 'skills',
   *       managedCellEditing : false
   *     }
   *   ]
   * });
   *
   * // From your custom editor, when you are ready to accept changes
   * grid.finishEditing();
   * ```
   *
   * @config {Boolean} managedCellEditing
   * @default true
   * @category Interaction
   * @advanced
   */
  { name: "managedCellEditing", defaultValue: true },
  /**
   * Setting this option means that pressing the `ESCAPE` key after editing the field will
   * revert the field to the value it had when the edit began. If the value is _not_ changed
   * from when the edit started, the input field's {@link Core.widget.Field#config-clearable}
   * behaviour will be activated. Finally, the edit will be canceled.
   * @config {Boolean} revertOnEscape
   * @default true
   * @category Interaction
   */
  { name: "revertOnEscape", defaultValue: true },
  /**
   * How to handle a request to complete a cell edit in this column if the field is invalid.
   * There are three choices:
   *  - `block` The default. The edit is not exited, the field remains focused.
   *  - `allow` Allow the edit to be completed.
   *  - `revert` The field value is reverted and the edit is completed.
   * @config {'block'|'allow'|'revert'} invalidAction
   * @default 'block'
   * @category Interaction
   */
  { name: "invalidAction", defaultValue: "block" },
  /**
   * Allow sorting of data in the column. You can pass true/false to enable/disable sorting, or provide a
   * custom sorting function, or a config object for a {@link Core.util.CollectionSorter}
   *
   * ```javascript
   * const grid = new Grid({
   *     columns : [
   *          {
   *              // Disable sorting for this column
   *              sortable : false
   *          },
   *          {
   *              field : 'name',
   *              // Custom sorting for this column
   *              sortable(user1, user2) {
   *                  return user1.name < user2.name ? -1 : 1;
   *              }
   *          },
   *          {
   *              // A config object for a Core.util.CollectionSorter
   *              sortable : {
   *                  property         : 'someField',
   *                  direction        : 'DESC',
   *                  useLocaleCompare : 'sv-SE'
   *              }
   *          }
   *     ]
   * });
   * ```
   * When providing a custom sorting function, if the sort feature is configured with
   * `prioritizeColumns : true` that function will also be used for programmatic sorting of the store:
   *
   * ```javascript
   * const grid = new Grid({
   *     features : {
   *       sort : {
   *           prioritizeColumns : true
   *       }
   *     },
   *
   *     columns : [
   *          {
   *              field : 'name',
   *              // Custom sorting for this column
   *              sortable(user1, user2) {
   *                  return user1.name < user2.name ? -1 : 1;
   *              }
   *          }
   *     ]
   * });
   *
   * // Will use sortable() from the column definition above
   * grid.store.sort('name');
   * ```
   *
   * @config {Boolean|Function|CollectionSorterConfig} sortable
   * @param {Core.data.Model} left Left side model to compare
   * @param {Core.data.Model} right Right side model to compare
   * @returns {Number}
   * @default true
   * @category Interaction
   */
  {
    name: "sortable",
    defaultValue: true,
    // Normalize function/object forms
    convert(value2, data, column) {
      if (!value2) {
        return false;
      }
      if (value2 === true) {
        return true;
      }
      const sorter = {};
      if (typeof value2 === "function") {
        sorter.originalSortFn = value2;
        sorter.sortFn = value2.bind(column);
      } else if (typeof value2 === "object") {
        Object.assign(sorter, value2);
        if (sorter.fn) {
          sorter.sortFn = sorter.fn;
          delete sorter.fn;
        }
      }
      return sorter;
    }
  },
  /**
   * Allow searching in the column (respected by QuickFind and Search features)
   * @config {Boolean} searchable
   * @default true
   * @category Interaction
   */
  { name: "searchable", defaultValue: true },
  /**
   * If `true`, this column will show a collapse/expand icon in its header, only applicable for parent columns
   * @config {Boolean} collapsible
   * @default false
   * @category Interaction
   */
  { name: "collapsible", defaultValue: false },
  /**
   * The collapsed state of this column, only applicable for parent columns
   * @config {Boolean} collapsed
   * @default false
   * @category Interaction
   */
  { name: "collapsed", defaultValue: false },
  /**
   * The collapse behavior when collapsing a parent column. Specify "toggleAll" or "showFirst".
   * * "showFirst" toggles visibility of all but the first columns.
   * * "toggleAll" toggles all children, useful if you have a special initially hidden column which gets shown
   * in collapsed state.
   * @config {String} collapseMode
   * @default 'showFirst'
   * @category Interaction
   */
  { name: "collapseMode" },
  /**
   * Allow filtering data in the column (if {@link Grid.feature.Filter} or {@link Grid.feature.FilterBar}
   * feature is enabled).
   *
   * Also allows passing a custom filtering function that will be called for each record with a single
   * argument of format `{ value, record, [operator] }`. Returning `true` from the function includes the
   * record in the filtered set.
   *
   * Configuration object may be used for {@link Grid.feature.FilterBar} feature to specify `filterField`. See
   * an example in the code snippet below or check {@link Grid.feature.FilterBar} page for more details.
   *
   * ```
   * const grid = new Grid({
   *     columns : [
   *          {
   *              field : 'name',
   *              // Disable filtering for this column
   *              filterable : false
   *          },
   *          {
   *              field : 'age',
   *              // Custom filtering for this column
   *              filterable: ({ value, record }) => Math.abs(record.age - value) < 10
   *          },
   *          {
   *              field : 'start',
   *              // Changing default field type
   *              filterable: {
   *                  filterField : {
   *                      type : 'datetime'
   *                  }
   *              }
   *          },
   *          {
   *              field : 'city',
   *              // Filtering for a value out of a list of values
   *              filterable: {
   *                  filterField : {
   *                      type  : 'combo',
   *                      value : '',
   *                      items : [
   *                          'Paris',
   *                          'Dubai',
   *                          'Montreal',
   *                          'London',
   *                          'New York'
   *                      ]
   *                  }
   *              }
   *          },
   *          {
   *              field : 'score',
   *              filterable : {
   *                  // This filter fn doesn't return 0 values as matching filter 'less than'
   *                  filterFn : ({ record, value, operator, property }) => {
   *                      switch (operator) {
   *                          case '<':
   *                              return record[property] === 0 ? false : record[property] < value;
   *                          case '=':
   *                              return record[property] == value;
   *                          case '>':
   *                              return record[property] > value;
   *                      }
   *                  }
   *              }
   *          }
   *     ]
   * });
   * ```
   *
   * When providing a custom filtering function, if the filter feature is configured with
   * `prioritizeColumns : true` that function will also be used for programmatic filtering of the store:
   *
   * ```javascript
   * const grid = new Grid({
   *     features : {
   *         filter : {
   *             prioritizeColumns : true
   *         }
   *     },
   *
   *     columns : [
   *          {
   *              field : 'age',
   *              // Custom filtering for this column
   *              filterable: ({ value, record }) => Math.abs(record.age - value) < 10
   *          }
   *     ]
   * });
   *
   * // Will use filterable() from the column definition above
   * grid.store.filter({
   *     property : 'age',
   *     value    : 50
   * });
   * ```
   *
   * To use custom `FilterField` combo `store` it should contain one of these
   * {@link Core.data.Store#config-data} or {@link Core.data.AjaxStore#config-readUrl} configs.
   * Otherwise combo will get data from owner Grid store.
   *
   * ```javascript
   * const grid = new Grid({
   *     columns : [
   *          {
   *              field : 'name',
   *              filterable: {
   *                  filterField {
   *                      type  : 'combo',
   *                      store : new Store({
   *                          data : ['Adam', 'Bob', 'Charlie']
   *                      })
   *                  }
   *              }
   *          }
   *     ]
   * });
   * ```
   *
   * or
   *
   * ```javascript
   * const grid = new Grid({
   *     columns : [
   *          {
   *              field : 'name',
   *              filterable: {
   *                  filterField : {
   *                     type  : 'combo',
   *                     store : new AjaxStore({
   *                         readUrl  : 'data/names.json',
   *                         autoLoad : true
   *                     })
   *                  }
   *              }
   *          }
   *     ]
   * });
   * ```
   *
   * @config {Boolean|Function|Object} filterable
   * @param {Object} data Data object
   * @param {*} data.value Record value
   * @param {Core.data.Model} data.record Record instance
   * @returns {Boolean} Returns `true` if value matches condition
   * @default true
   * @category Interaction
   */
  {
    name: "filterable",
    defaultValue: true,
    // Normalize function/object forms
    convert(value2, data, column) {
      if (!value2) {
        return false;
      }
      if (value2 === true) {
        return true;
      }
      const filter2 = {
        columnOwned: true
      };
      if (typeof value2 === "function") {
        filter2.filterFn = value2;
      } else if (typeof value2 === "object") {
        ObjectHelper.merge(filter2, column.constructor.defaultValues.filterable, value2);
      }
      return filter2;
    }
  },
  /**
   * Setting this flag to `true` will prevent dropping child columns into a group column
   * @config {Boolean} sealed
   * @default false
   * @category Interaction
   */
  { name: "sealed" },
  /**
   * Allow column visibility to be toggled through UI
   * @config {Boolean} hideable
   * @default true
   * @category Interaction
   */
  { name: "hideable", defaultValue: true },
  /**
   * Set to `false` to prevent this column header from being dragged
   * @config {Boolean} draggable
   * @category Interaction
   */
  { name: "draggable", defaultValue: true },
  /**
   * Set to `false` to prevent grouping by this column
   * @config {Boolean} groupable
   * @category Interaction
   */
  { name: "groupable", defaultValue: true },
  /**
   * Set to `false` to prevent the column from being drag-resized when the ColumnResize plugin is enabled.
   * @config {Boolean} resizable
   * @default true
   * @category Interaction
   */
  { name: "resizable", defaultValue: true },
  //endregion
  //region Rendering
  /**
   * Renderer function for group headers (when using Group feature).
   *
   * <div class="note">
   * You should never modify any records inside this method.
   * </div>
   *
   * ```javascript
   * const grid = new Grid({
   *     columns : [
   *         {
   *             text : 'ABC',
   *             groupRenderer(renderData) {
   *                 return {
   *                      class : {
   *                          big   : true,
   *                          small : false
   *                      },
   *                      children : [
   *                          { tag : 'img', src : 'img.png' },
   *                          renderData.groupRowFor
   *                      ]
   *                 };
   *             }
   *         }
   *     ]
   * });
   * ```
   *
   * @config {Function} groupRenderer
   * @param {Object} renderData
   * @param {HTMLElement} renderData.cellElement Cell element, for adding CSS classes, styling etc.
   * @param {*} renderData.groupRowFor Current group value
   * @param {Core.data.Model} renderData.record Record for the row
   * @param {Core.data.Model[]} renderData.groupRecords Records in the group
   * @param {Grid.column.Column} renderData.column Current rendering column
   * @param {Grid.column.Column} renderData.groupColumn Column that the grid is grouped by
   * @param {Number} renderData.count Number of records in the group
   * @param {Grid.view.Grid} renderData.grid This grid
   * @returns {DomConfig|String|null} The header grouping text or DomConfig object representing the HTML markup
   *
   * @category Rendering
   */
  "groupRenderer",
  /**
   * Renderer function for the column header.
   *
   * @config {Function} headerRenderer
   * @param {Object} renderData
   * @param {Grid.column.Column} renderData.column This column
   * @param {HTMLElement} renderData.headerElement The header element
   * @returns {String|null} The text or markup to show in the column header
   * @typings {String|Object|null|void} // Fix for React JSX
   *
   * @category Rendering
   */
  "headerRenderer",
  /**
   * A tooltip string to show when hovering the column header, or a config object which can
   * reconfigure the shared tooltip by setting boolean, numeric and string config values.
   * @config {String|TooltipConfig} tooltip
   * @category Rendering
   */
  "tooltip",
  /**
   * Renderer function for the cell tooltip (used with {@link Grid.feature.CellTooltip} feature).
   * Specify `false` to disable tooltip for this column.
   *
   * <div class="note">
   * You should never modify any records inside this method.
   * </div>
   *
   * @config {Function|Boolean} tooltipRenderer
   * @param {Object} renderData
   * @param {HTMLElement} renderData.cellElement Cell element
   * @param {Core.data.Model} renderData.record Record for cell row
   * @param {Grid.column.Column} renderData.column Cell column
   * @param {Grid.feature.CellTooltip} renderData.cellTooltip Feature instance, used to set tooltip content async
   * @param {MouseEvent} renderData.event The event that triggered the tooltip
   * @returns {String|DomConfig|null}
   *
   * @category Rendering
   */
  "tooltipRenderer",
  /**
   * CSS class added to each cell in this column
   * @prp {String} cellCls
   * @category Rendering
   */
  "cellCls",
  /**
   * CSS class added to the header of this column
   * @config {String} cls
   * @category Rendering
   */
  "cls",
  /**
   * Icon to display in header. Specifying an icon will render a `<i>` element with the icon as value for the
   * class attribute
   * @prp {String} icon
   * @category Rendering
   */
  "icon",
  //endregion
  //region Layout
  /**
   * Text align. Accepts `'left'`/`'center'`/`'right'` or direction neutral `'start'`/`'end'`
   * @config {'left'|'center'|'right'|'start'|'end'} align
   * @category Layout
   */
  "align",
  /**
   * Column minimum width. If value is `Number`, then minimal width is in pixels
   * @config {Number|String} minWidth
   * @default 60
   * @category Layout
   */
  { name: "minWidth", defaultValue: 60 },
  /**
   * Column maximal width. If value is Number, then maximal width is in pixels
   * @config {Number|String} maxWidth
   * @category Common
   */
  "maxWidth",
  /**
   * Columns hidden state. Specify `true` to hide the column, `false` to show it.
   * @prp {Boolean} hidden
   * @category Layout
   */
  { name: "hidden", defaultValue: false },
  /**
   * Convenient way of putting a column in the "locked" region. Same effect as specifying region: 'locked'.
   * If you have defined your own regions (using {@link Grid.view.Grid#config-subGridConfigs}) you should use
   * {@link #config-region} instead of this one.
   * @config {Boolean} locked
   * @default false
   * @category Layout
   */
  { name: "locked" },
  /**
   * Region (part of the grid, it can be configured with multiple) where to display the column. Defaults to
   * {@link Grid.view.Grid#config-defaultRegion}.
   *
   * A column under a grouped header automatically belongs to the same region as the grouped header.
   *
   * @config {String} region
   * @category Layout
   */
  { name: "region" },
  /**
   * Specify `true` to merge cells within the column whose value match between rows, making the first
   * occurrence of the value span multiple rows.
   *
   * Only applies when using the {@link Grid/feature/MergeCells MergeCells feature}.
   *
   * This setting can also be toggled using the column header menu.
   *
   * @config {Boolean} mergeCells
   * @category Merge cells
   */
  { name: "mergeCells", type: "boolean" },
  /**
   * Set to `false` to prevent merging cells in this column using the column header menu.
   *
   * Only applies when using the {@link Grid/feature/MergeCells MergeCells feature}.
   *
   * @config {Boolean} mergeable
   * @default true
   * @category Merge cells
   */
  { name: "mergeable", type: "boolean", defaultValue: true },
  /**
   * An empty function by default, but provided so that you can override it. This function is called each time
   * a merged cell is rendered. It allows you to manipulate the DOM config object used before it is synced to
   * DOM, thus giving you control over styling and contents.
   *
   * NOTE: The function is intended for formatting, you should not update records in it since updating records
   * triggers another round of rendering.
   *
   * ```javascript
   * const grid = new Grid({
   *   columns : [
   *     {
   *       field      : 'project',
   *       text       : 'Project',
   *       mergeCells : 'true,
   *       mergedRenderer({ domConfig, value, fromIndex, toIndex }) {
   *         domConfig.className.highlight = value === 'Important project';
   *       }
   *    }
   *  ]
   * });
   * ```
   *
   * @config {Function} mergedRenderer
   * @param {Object} detail An object containing the information needed to render a task.
   * @param {*} detail.value Value that will be displayed in the merged cell
   * @param {Number} detail.fromIndex Index in store of the first row of the merged cell
   * @param {Number} detail.toIndex Index in store of the last row of the merged cell
   * @param {DomConfig} detail.domConfig DOM config object for the merged cell
   * element
   * @returns {void}
   * @category Merge cells
   */
  "mergedRenderer",
  //endregion
  // region Menu
  /**
   * Show column picker for the column
   * @config {Boolean} showColumnPicker
   * @default true
   * @category Menu
   */
  { name: "showColumnPicker", defaultValue: true },
  /**
   * false to prevent showing a context menu on the column header element
   * @config {Boolean} enableHeaderContextMenu
   * @default true
   * @category Menu
   */
  { name: "enableHeaderContextMenu", defaultValue: true },
  /**
   * Set to `false` to prevent showing a context menu on the cell elements in this column
   * @config {Boolean} enableCellContextMenu
   * @default true
   * @category Menu
   */
  { name: "enableCellContextMenu", defaultValue: true },
  /**
   * Extra items to show in the header context menu for this column.
   *
   * ```javascript
   * headerMenuItems : {
   *     customItem : { text : 'Custom item' }
   * }
   * ```
   *
   * @config {Object<String,MenuItemConfig|Boolean|null>} headerMenuItems
   * @category Menu
   */
  "headerMenuItems",
  /**
   * Extra items to show in the cell context menu for this column, `null` or `false` to not show any menu items
   * for this column.
   *
   * ```javascript
   * cellMenuItems : {
   *     customItem : { text : 'Custom item' }
   * }
   * ```
   *
   * @config {Object<String,MenuItemConfig|Boolean|null>} cellMenuItems
   * @category Menu
   */
  "cellMenuItems",
  //endregion
  //region Summary
  /**
   * Summary type (when using Summary feature). Valid types are:
   *
   * * `'sum'` - Sum of all values in the column
   * * `'add'` - Alias for sum
   * * `'count'` - Number of rows
   * * `'countNotEmpty'` - Number of rows containing a value
   * * `'average'` - Average of all values in the column
   * * `callbackFn` - A custom function, used with `store.reduce`. Its return value becomes the value of the accumulator parameter on the next invocation of callbackFn
   * </dl>
   * @config {'sum'|'add'|'count'|'countNotEmpty'|'average'|Function} sum
   * @param {Number|*} result The value resulting from the previous call to callbackFn. On the first call, its value is initialValue if the latter is specified; otherwise its value is first element.
   * @param {Core.data.Model} value The value of the current element.
   * @param {Number} index The index position of currentValue. On the first call, its value is 0 if initialValue is specified, otherwise 1.
   * @returns {Number|*}
   * @category Summary
   */
  "sum",
  /**
   * Summary configs, use if you need multiple summaries per column. Replaces {@link #config-sum} and
   * {@link #config-summaryRenderer} configs.
   * @config {ColumnSummaryConfig[]} summaries
   * @category Summary
   */
  "summaries",
  /**
   * Renderer function for summary (when using Summary feature). The renderer is called with an object having
   * the calculated summary `sum` parameter. Function returns a string value to be rendered.
   *
   * Example:
   *
   * ```javascript
   * columns : [{
   *     type            : 'number',
   *     text            : 'Score',
   *     field           : 'score',
   *     sum             : 'sum',
   *     summaryRenderer : ({ sum }) => `Total amount: ${sum}`
   * }]
   * ```
   *
   * @config {Function} summaryRenderer
   * @param {Object} data Object containing renderer parameters
   * @param {Number|*} data.sum The sum parameter
   * @returns {String|DomConfig|null}
   * @category Summary
   */
  "summaryRenderer",
  //endregion
  //region Misc
  /**
   * Column settings at different responsive levels, see responsive demo under examples/
   * @config {Object} responsiveLevels
   * @category Misc
   */
  "responsiveLevels",
  /**
   * Tags, may be used by ColumnPicker feature for grouping columns by tag in the menu
   * @config {String[]} tags
   * @category Misc
   */
  "tags",
  /**
   * Column config to apply to normal config if viewed on a touch device
   * @config {GridColumnConfig} touchConfig
   * @category Misc
   */
  "touchConfig",
  /**
   * When using the tree feature, exactly one column should specify { tree: true }
   * @prp {Boolean} tree
   * @readonly
   * @category Misc
   */
  "tree",
  /**
   * Determines which type of filtering to use for the column. Usually determined by the column type used,
   * but may be overridden by setting this field.
   * @config {'text'|'date'|'number'|'duration'} filterType
   * @category Misc
   */
  "filterType",
  /**
   * By default, any rendered column cell content is HTML-encoded. Set this flag to `false` disable this and
   * allow rendering html elements
   * @config {Boolean} htmlEncode
   * @default true
   * @category Misc
   */
  { name: "htmlEncode", defaultValue: true },
  /**
   * By default, the header text is HTML-encoded. Set this flag to `false` disable this and allow html
   * elements in the column header
   * @config {Boolean} htmlEncodeHeaderText
   * @default true
   * @category Misc
   */
  { name: "htmlEncodeHeaderText", defaultValue: true },
  /**
   * Set to `true`to automatically call DomHelper.sync for html returned from a renderer. Should in most cases
   * be more performant than replacing entire innerHTML of cell and also allows CSS transitions to work. Has
   * no effect unless {@link #config-htmlEncode} is disabled. Returned html must contain a single root element
   * (that can have multiple children). See PercentColumn for example usage.
   * @config {Boolean} autoSyncHtml
   * @default false
   * @category Misc
   */
  { name: "autoSyncHtml", defaultValue: false },
  /**
   * Set to `false` to not always clear cell content if the {@link #config-renderer} returns `undefined`
   * or has no `return` statement. This is useful when you mutate the cellElement, and want to prevent
   * cell content from being reset during rendering.
   *
   * Set to `true` to always clear cell content regardless of renderer return value.
   * @config {Boolean} alwaysClearCell
   * @default true
   * @category Misc
   */
  { name: "alwaysClearCell", defaultValue: true },
  /**
   * An array of the widgets to append to the column header. These widgets have this Column instance
   * as their `owner` which can be used to reference the column, and the owning Grid via `this.owner.grid`.
   * ```javascript
   * columns : [
   * {
   *     text          : 'Name',
   *     field         : 'name',
   *     flex          : 1,
   *     headerWidgets : [
   *         {
   *             type   : 'button',
   *             text   : 'Add row',
   *             cls    : 'b-raised b-blue',
   *             async onAction() {
   *                 const
   *                      grid = this.owner.grid,
   *                      [newRecord] = grid.store.add({
   *                          name : 'New user'
   *                      });
   *
   *                 await grid.scrollRowIntoView(newRecord);
   *
   *                 await grid.features.cellEdit.startEditing({
   *                     record : newRecord,
   *                     field  : 'name'
   *                 });
   *             }
   *         }
   *     ]
   * }]
   * ```
   * @prp {ContainerItemConfig[]} headerWidgets
   * @category Misc
   */
  { name: "headerWidgets" },
  /**
   * Set to `true` to have the {@link Grid.feature.CellEdit} feature update the record being edited live upon
   * field edit instead of when editing is finished by using `TAB` or `ENTER`
   * @config {Boolean} instantUpdate
   * @category Misc
   */
  { name: "instantUpdate", defaultValue: false },
  { name: "repaintOnResize", defaultValue: false },
  /**
   * An optional query selector to select a sub element within the cell being
   * edited to align a cell editor's `X` position and `width` to.
   * @config {String} editTargetSelector
   * @category Misc
   */
  "editTargetSelector",
  //endregion
  //region Export
  /**
   * Used by the Export feature. Set to `false` to omit a column from an exported dataset
   * @config {Boolean} exportable
   * @default true
   * @category Export
   */
  { name: "exportable", defaultValue: true },
  /**
   * Column type which will be used by {@link Grid.util.TableExporter}. See list of available types in
   * TableExporter docs. Returns undefined by default, which means column type should be read from the record
   * field.
   * @config {String} exportedType
   * @category Export
   */
  { name: "exportedType" },
  //endregion
  /**
   * The `aria-label` to use for this Column`s header element
   * @config {String} ariaLabel
   * @category Accessibility
   */
  {
    name: "ariaLabel",
    defaultValue: "L{Column.columnLabel}"
  },
  /**
   * The `aria-label` to use for cells in this Column
   * @config {String} cellAriaLabel
   * @category Accessibility
   */
  {
    name: "cellAriaLabel",
    defaultValue: "L{Column.cellLabel}"
  },
  /**
   * Flag to enable vue component rendering
   * @config {Boolean} vue
   * @category Integration
   */
  "vue",
  /**
   * Set to `true` to have the cell editor for this column inherit formula providers from the Grid's configured
   * {@link Grid.view.GridBase#config-formulaProviders}.
   * @config {Boolean} formula
   */
  "formula"
]);
// prevent undefined fields from being exposed, to simplify spotting errors
__publicField(_Column, "autoExposeFields", false);
var Column = _Column;
Column.emptyCount = 0;
Column.defaultWidth = 100;
Column.exposeProperties();
Column._$name = "Column";

// ../Grid/lib/Grid/data/ColumnStore.js
var columnDefinitions = {
  boolean: {
    type: "check"
  },
  date: {
    type: "date"
  },
  integer: {
    type: "number",
    format: {
      maximumFractionDigits: 0
    }
  },
  number: {
    type: "number"
  }
};
var lockedColumnSorters = [{
  field: "region"
}];
var _ColumnStore = class _ColumnStore extends Localizable_default(Store) {
  construct(config) {
    const me = this, { grid } = config;
    if (grid) {
      grid._columnStore = me;
      me.id = `${grid.id}-columns`;
      grid.ion({
        subGridCollapse: "clearSubGridCaches",
        subGridExpand: "clearSubGridCaches",
        thisObj: me
      });
    }
    super.construct(config);
    me.ion({
      change: me.onStoreChange,
      sort: () => me.updateChainedStores(),
      thisObj: me,
      prio: 1
    });
  }
  doDestroy() {
    const allColumns = [];
    if (!this.isChained) {
      this.traverse((column) => allColumns.push(column));
    }
    super.doDestroy();
    if (!this.isChained) {
      allColumns.forEach((column) => column.destroy());
    }
  }
  // SubGrid chained ColumnStores are in a special state. They must not have the tree flag set
  // but must still yield isTree as true, so the isTree method must defer to the master store.
  chain(chainedFilterFn, chainedFields, config) {
    return super.chain(chainedFilterFn, chainedFields, {
      ...config,
      tree: null
    });
  }
  // Overridden because the flat collection only contains top level columns,
  // not leaves - group columns are *not* expanded.
  /**
   * Get column by id.
   * @param {String|Number} id
   * @returns {Grid.column.Column}
   */
  getById(id2) {
    return super.getById(id2) || this.idRegister[id2];
  }
  forEach(fn2, thisObj = this) {
    this.traverseWhile((n, i) => fn2.call(thisObj, n, i), true);
  }
  get totalFixedWidth() {
    let result = 0;
    for (const col of this) {
      if (!col.hidden) {
        if (col.children) {
          col.children.forEach((childCol) => result += this.calculateFixedWidth(childCol));
        } else {
          result += this.calculateFixedWidth(col);
        }
      }
    }
    return result;
  }
  get hasFlex() {
    return this.visibleColumns.some((column) => column.flex);
  }
  calculateFixedWidth(column) {
    if (column.flex) {
      return column.measureSize(Column.defaultWidth);
    } else {
      return Math.max(column.measureSize(column.width), column.measureSize(column.minWidth));
    }
  }
  /**
   * Returns the top level columns. If using grouped columns, this is the top level columns. If no grouped
   * columns are being used, this is the leaf columns.
   * @property {Grid.column.Column[]}
   * @readonly
   */
  get topColumns() {
    return this.isChained ? this.masterStore.rootNode.children.filter(this.chainedFilterFn) : this.rootNode.children;
  }
  /**
   * Returns the visible leaf headers which drive the rows' cell content.
   * @property {Grid.column.Column[]}
   * @readonly
   */
  get visibleColumns() {
    const me = this;
    if (!me._visibleColumns) {
      me._visibleColumns = me.leaves.filter((column) => column.isVisible && (!column.subGrid || !column.subGrid.collapsed)).sort(me.sorterFn);
    }
    return me._visibleColumns;
  }
  onStoreChange({ action, changes }) {
    if (action === "update" && !("hidden" in changes)) {
      return;
    }
    this.clearCaches();
  }
  clearSubGridCaches({ subGrid }) {
    subGrid.columns.clearCaches();
    this.clearCaches();
  }
  clearCaches() {
    var _a5;
    this._visibleColumns = null;
    (_a5 = this.masterStore) == null ? void 0 : _a5.clearCaches();
  }
  onMasterDataChanged(event) {
    super.onMasterDataChanged(event);
    if (event.action !== "update" || ("hidden" in event.changes || "region" in event.changes)) {
      this.clearCaches();
    }
  }
  getAdjacentVisibleLeafColumn(columnOrId, next = true, wrap = false) {
    const columns = this.visibleColumns, column = columnOrId instanceof Column ? columnOrId : this.getById(columnOrId);
    let idx = columns.indexOf(column) + (next ? 1 : -1);
    if (!columns[idx]) {
      if (wrap) {
        idx = next ? 0 : columns.length - 1;
      } else {
        return null;
      }
    }
    return columns[idx];
  }
  /**
   * Bottom columns are the ones displayed in the bottom row of a grouped header, or all columns if not using a grouped
   * header. They are the columns that actually display any data.
   * @property {Grid.column.Column[]}
   * @readonly
   */
  get bottomColumns() {
    return this.leaves;
  }
  /**
   * Get column by field. To be sure that you are getting exactly the intended column, use {@link Core.data.Store#function-getById Store#getById()} with the
   * columns id instead.
   * @param {String} field Field name
   * @returns {Grid.column.Column}
   */
  get(field) {
    return this.findRecord("field", field, true);
  }
  lookupStateColumns(config) {
    const { field, type, id: id2 } = config, column = this.getById(id2);
    if (!column && (field || type)) {
      return this.query((col) => {
        var _a5;
        return (!field || col.field === field) && (!type || col.type === type) && (!id2 || ((_a5 = col.meta) == null ? void 0 : _a5.generatedId) || col.id === id2);
      });
    }
    return [column];
  }
  lookupStateColumn(config) {
    return this.lookupStateColumns(config)[0];
  }
  /**
   * Used internally to create a new record in the store. Creates a column of the correct type by looking up the
   * specified type among registered columns.
   * @private
   */
  createRecord(data) {
    var _a5, _b2;
    const { grid = {} } = this, { store } = grid, dataField = (_b2 = (_a5 = store == null ? void 0 : store.modelClass) == null ? void 0 : _a5.fieldMap) == null ? void 0 : _b2[data.field];
    let columnClass = this.modelClass;
    if (dataField == null ? void 0 : dataField.column) {
      data = Objects.merge({}, dataField.column, data);
    }
    if (data.type) {
      columnClass = _ColumnStore.getColumnClass(data.type);
      if (!columnClass) {
        throw new Error(`Column type '${data.type}' not registered. Please make sure you have imported the column class.`);
      }
    }
    if (data.locked) {
      data.region = "locked";
      delete data.locked;
    }
    const column = new columnClass(data, this);
    if (!column.data.region) {
      column.data.region = grid.defaultRegion || "normal";
    }
    if (this.autoAddField && !column.noFieldSpecified && store && !dataField) {
      let fieldDefinition = column.field;
      if (column.constructor.fieldType) {
        fieldDefinition = {
          name: column.field,
          type: column.constructor.fieldType
        };
      }
      store.modelClass.addField(fieldDefinition);
    }
    return column;
  }
  /**
   * indexOf extended to also accept a columns field, for backward compatibility.
   *
   * ```javascript
   * grid.columns.indexOf('name');
   * ```
   *
   * @param {Core.data.Model|String} recordOrId
   * @returns {Number}
   */
  indexOf(recordOrId) {
    if (recordOrId == null) {
      return -1;
    }
    const index = super.indexOf(recordOrId);
    if (index > -1)
      return index;
    return this.records.findIndex((r) => r.field === recordOrId);
  }
  /**
   * Removes all columns.
   * @param {Boolean} [silent] Specify `true` to suppress events
   * @returns {Boolean} `true` unless the action was prevented, in which case it returns `false`
   * @fires beforeRemove
   * @fires removeAll
   * @fires Core.data.Store#event-change
   * @category CRUD
   */
  removeAll(silent = false) {
    const me = this, isTimeAxis = me.some((i) => i.isTimeAxisColumn);
    if (isTimeAxis) {
      return me.remove(me.query((i) => !i.isTimeAxisColumn), silent);
    }
    return super.removeAll(silent);
  }
  /**
   * Checks if any column uses autoHeight
   * @internal
   * @property {Boolean}
   * @readonly
   */
  get usesAutoHeight() {
    return this.some((column) => column.autoHeight);
  }
  /**
   * Checks if any flex column uses autoHeight
   * @internal
   * @property {Boolean}
   * @readonly
   */
  get usesFlexAutoHeight() {
    return this.some((column) => column.autoHeight && column.flex != null);
  }
  // Let syncDataOnLoad match on id, field or type (in that order)
  resolveSyncNode(rawData) {
    if (rawData.id) {
      return super.resolveSyncNode(rawData);
    }
    if (rawData.field) {
      return {
        id: rawData.field,
        node: this.allRecords.find((r) => r.field === rawData.field)
      };
    }
    if (rawData.type) {
      return {
        id: rawData.type,
        node: this.allRecords.find((r) => r.type === rawData.type)
      };
    }
    return { id: null, node: null };
  }
  //region Column types
  /**
   * Call from custom column to register it with ColumnStore. Required to be able to specify type in column config.
   * @param {Function} columnClass The {@link Grid.column.Column} subclass to register.
   * @param {Boolean} simpleRenderer Pass `true` if its default renderer does *not* use other fields from the passed
   * record than its configured {@link Grid.column.Column#config-field}. This enables more granular cell updating
   * upon record mutation.
   *
   * ```javascript
   * // create and register custom column
   * class CustomColumn {
   *  static get type() {
   *      return 'custom';
   *  }
   * }
   * ColumnStore.registerColumnType(CustomColumn, true);
   * // now possible to specify in column config
   * let grid = new Grid({
   *   columns: [
   *     { type: 'custom', field: 'id' }
   *   ]
   * });
   * ```
   */
  static registerColumnType(columnClass, simpleRenderer = false) {
    columnClass.simpleRenderer = simpleRenderer;
    (_ColumnStore.columnTypes || (_ColumnStore.columnTypes = {}))[columnClass.type] = columnClass;
  }
  /**
   * Returns registered column class for specified type.
   * @param type Type name
   * @returns {Grid.column.Column}
   * @internal
   */
  static getColumnClass(type) {
    return _ColumnStore.columnTypes && _ColumnStore.columnTypes[type];
  }
  /**
   * Generates a <strong>new </strong> {@link Grid.column.Column} instance which may be subsequently added to this
   * store to represent the passed {@link Core.data.field.DataField} of the owning Grid's store.
   * @param {Core.data.field.DataField|String} dataField The {@link Core.data.field.DataField field}
   * instance or field name to generate a new {@link Grid.column.Column} for.
   *
   * ```javascript
   * // Add column for the "team" field.
   * grid.columns.add(grid.columns.generateColumnForField('team', {
   *     width : 200
   * }));
   * ```
   *
   * @param {Object} [defaults] Defaults to apply to the new column.
   * @returns {Grid.column.Column} A new Column which will render and edit the field correctly.
   * @internal
   */
  generateColumnForField(dataField, defaults) {
    var _a5;
    if (typeof dataField === "string" && this.grid) {
      dataField = (_a5 = this.grid.store) == null ? void 0 : _a5.modelClass.fieldMap[dataField];
    }
    let column = dataField.column || columnDefinitions[dataField.type] || {};
    if (typeof column === "string") {
      column = { type: column };
    }
    column = Object.assign({
      text: dataField.text || StringHelper.separate(dataField.name),
      field: dataField.name
    }, defaults, column);
    if (dataField.precision != null) {
      column.format.maximumFractionDigits = dataField.precision;
    }
    if (dataField.columnType) {
      column.type = dataField.columnType;
    }
    return this.createRecord(column);
  }
  //endregion
};
/**
 * @hideconfigs lazyLoad, autoLoad, remoteFilter, pageSize, remotePaging, remoteSort, useRawData
 */
/**
 * @hideproperties lazyLoad, isPaged, lastPage, pageSize
 */
/**
 * @hidefunctions requestData, loadPage, nextPage, previousPage
 */
/**
 * @hideevents lazyLoadStarted, lazyLoadEnded, afterRequest, beforeLoadPage, beforeRequest, loadPage, requestData
 */
//region Events
/**
 * Fires when a column is shown.
 * @event columnShow
 * @param {Grid.data.ColumnStore} source The store which triggered the event.
 * @param {Grid.column.Column} column The column which status has been changed.
 */
/**
 * Fires when a column has been hidden.
 * @event columnHide
 * @param {Grid.data.ColumnStore} source The store which triggered the event.
 * @param {Grid.column.Column} column The column which status has been changed.
 */
//endregion
__publicField(_ColumnStore, "configurable", {
  modelClass: Column,
  tree: true,
  /**
   * Automatically adds a field definition to the store used by the Grid when adding a new Column displaying a
   * non-existing field.
   *
   * To enable this behaviour:
   *
   * ```javascript
   * const grid = new Grid({
   *     columns : {
   *         autoAddField : true,
   *         data         : [
   *             // Column definitions here
   *         ]
   *     }
   * }
   * ```
   *
   * @config {Boolean}
   * @default
   */
  autoAddField: false,
  /**
   * `ColumnStore` uses `syncDataOnLoad` by default (with `threshold : 1`), to ensure good performance when
   * binding to columns in frameworks.
   *
   * See {@link Core/data/Store#config-syncDataOnLoad} for more information.
   *
   * @config {Boolean|SyncDataOnLoadOptions}
   * @default true
   * @readonly
   */
  syncDataOnLoad: {
    threshold: 1
  },
  // Locked columns must sort to before non-locked
  sorters: lockedColumnSorters,
  // Make sure regions stick together when adding columns
  reapplySortersOnAdd: true,
  useRawData: false
});
var ColumnStore = _ColumnStore;
var columnResizeEvent = (handler, thisObj) => ({
  update: ({ store, record, changes }) => {
    let result = true;
    if ("width" in changes || "minWidth" in changes || "maxWidth" in changes || "flex" in changes) {
      result = handler.call(thisObj, { store, record, changes });
    }
    return result;
  }
});
ColumnStore.registerColumnType(Column, true);
ColumnStore._$name = "ColumnStore";

// ../Grid/lib/Grid/column/ActionColumn.js
var ActionColumn = class extends Column {
  get groupHeaderReserved() {
    return true;
  }
  construct(config, store) {
    const me = this;
    if (config.renderer) {
      VersionHelper.deprecate("grid", "7.0.0", "Using deprecated `renderer` with ActionColumn, use `afterRenderCell` callback instead.");
      config.afterRenderCell = config.renderer;
      delete config.renderer;
    }
    super.construct(config, store);
    if (!config.width && !config.flex) {
      me.grid.ion({ paint: "updateAutoWidth", thisObj: me });
    }
    if (me.disableIfGridReadOnly) {
      me.grid.element.classList.add("b-actioncolumn-readonly");
    }
  }
  /**
   * Renderer that displays action icon(s) in the cell.
   * @private
   */
  defaultRenderer({ grid, column, record, isExport, callExternalRenderer = true }) {
    var _a5;
    const inGroupTitle = record && "groupRowFor" in record.meta, { subGrid } = column, renderConfig = {
      className: { "b-action-ct": 1 },
      children: (_a5 = column.actions) == null ? void 0 : _a5.map((actionConfig, index) => {
        var _a6, _b2;
        if ("visible" in actionConfig) {
          if (typeof actionConfig.visible === "function" && actionConfig.visible({ record }) === false || actionConfig.visible === false) {
            return "";
          }
        }
        if (inGroupTitle && !actionConfig.showForGroup || !inGroupTitle && actionConfig.showForGroup) {
          return "";
        }
        const {
          tooltip,
          renderer
        } = actionConfig;
        let btip = typeof tooltip === "function" || ((_a6 = tooltip == null ? void 0 : tooltip.startsWith) == null ? void 0 : _a6.call(tooltip, "up.")) ? subGrid.callback(tooltip, subGrid, [{ record }]) : tooltip || "";
        if (typeof btip === "string") {
          btip = this.optionalL(btip, this);
        }
        let config;
        if (renderer) {
          const customRendererData = (_b2 = subGrid.callback(renderer, subGrid, [{
            index,
            record,
            column,
            isExport,
            tooltip: btip,
            action: actionConfig
          }])) != null ? _b2 : "";
          if (typeof customRendererData === "string") {
            config = {
              tag: "span",
              class: "b-action-item b-tool",
              dataset: {
                ...Tooltip.encodeConfig(btip),
                index
              },
              html: customRendererData
            };
          } else {
            config = customRendererData;
            (config.dataset || (config.dataset = {})).index = index;
            (config.className || (config.className = {}))["b-action-item"] = 1;
            config.className[actionConfig.cls] = actionConfig.cls;
          }
        } else {
          config = {
            tag: "button",
            dataset: {
              ...Tooltip.encodeConfig(btip),
              index
            },
            className: {
              "b-tool": 1,
              "b-action-item": 1,
              [actionConfig.cls]: actionConfig.cls
            }
          };
        }
        const ariaLabel = actionConfig.ariaLabel || config.dataset.btip;
        if (ariaLabel) {
          config["aria-label"] = StringHelper.encodeHtml(ariaLabel);
        }
        return config;
      })
    };
    if (isExport) {
      return renderConfig.children.flatMap((configItem) => configItem.html || []).join(",");
    }
    return renderConfig;
  }
  /**
   * Handle icon click and call action handler.
   * @private
   */
  onCellClick({ grid, column, record, target }) {
    var _a5;
    let actionEl;
    if (column !== this || !(actionEl = target.closest(".b-action-item[data-index]"))) {
      return;
    }
    const actionIndex = actionEl.dataset.index, action = (_a5 = column.actions) == null ? void 0 : _a5[actionIndex], actionHandler = action == null ? void 0 : action.onClick;
    if (actionHandler) {
      this.callback(actionHandler, column, [{ record, action, target, grid, column }]);
    }
  }
  /**
   * Update width for actions column to fit content.
   * @private
   */
  updateAutoWidth() {
    const me = this, groupActions = [], {
      actions: oldActions
    } = me;
    if (!me.element) {
      return;
    }
    const actions5 = me.actions = [];
    oldActions == null ? void 0 : oldActions.forEach((actionOriginal) => {
      const action = { ...actionOriginal };
      delete action.visible;
      if (action.showForGroup) {
        delete action.showForGroup;
        groupActions.push(action);
      } else {
        actions5.push(action);
      }
    });
    if (groupActions.length > actions5.length) {
      me._actions = groupActions;
    }
    const actionsHtml = DomHelper.createElement(me.defaultRenderer({
      column: me,
      record: new me.grid.store.modelClass(),
      callExternalRenderer: false
    })).outerHTML;
    me.width = DomHelper.measureText(actionsHtml, me.element, true, me.element.parentElement);
    me.actions = oldActions;
  }
};
__publicField(ActionColumn, "type", "action");
__publicField(ActionColumn, "$name", "ActionColumn");
__publicField(ActionColumn, "fields", [
  /**
   * An array of action config objects, see {@link #typedef-ActionConfig} for details.
   *
   * ```javascript
   * new Grid({
   *     columns  : [{
   *         type    : 'action',
   *         text    : 'Actions',
   *         actions : [{
   *             cls      : 'b-fa b-fa-plus',
   *             visible  : ({ record }) => record.canAdd,
   *             onClick  : ({ record }) => console.log(`Adding ${record.name}`)
   *         }, {
   *             cls     : 'b-fa b-fa-pencil',
   *             tooltip : 'Edit note',
   *             onClick : ({ record }) => console.log(`Editing ${record.name}`)
   *         }]
   *     }]
   * });
   * ```
   *
   * @config {ActionConfig[]} actions List of action configs
   * @category Common
   */
  { name: "actions", type: "array" },
  /**
   * Set true to hide disable actions in this column if the grid is {@link Core.widget.Widget#config-readOnly}
   * @config {Boolean} disableIfGridReadOnly
   * @default
   * @category Common
   */
  { name: "disableIfGridReadOnly", defaultValue: false }
]);
__publicField(ActionColumn, "defaults", {
  /**
   * @hideconfigs filterable, groupable, sortable, editor, searchable, htmlEncode, resizable
   */
  /**
   * Column minimal width. If value is Number then minimal width is in pixels.
   * @config {Number|String} minWidth
   * @default 30
   * @category Layout
   */
  minWidth: 30,
  filterable: false,
  groupable: false,
  sortable: false,
  editor: false,
  searchable: false,
  htmlEncode: false,
  resizable: false,
  readOnly: true,
  ariaLabel: "L{ActionColumn.rowActions}"
});
ColumnStore.registerColumnType(ActionColumn);
ActionColumn.exposeProperties();
ActionColumn._$name = "ActionColumn";

// ../Grid/lib/Grid/column/NumberColumn.js
var NumberColumn = class extends Column {
  //endregion
  //region Init
  get defaultEditor() {
    const { format: format2, name, max: max3, min: min4, step, largeStep, align } = this;
    return ObjectHelper.cleanupProperties({
      type: "numberfield",
      format: format2,
      name,
      max: max3,
      min: min4,
      step,
      largeStep,
      textAlign: align
    });
  }
  get formatter() {
    const me = this, { format: format2 } = me;
    let formatter = me._formatter;
    if (!formatter || me._lastFormat !== format2) {
      me._formatter = formatter = NumberHelper.getLocalizedFormatter(me._lastFormat = format2);
    }
    return formatter;
  }
  formatValue(value2, addUnit = true) {
    if (value2 != null) {
      value2 = this.formatter.format(value2);
      if (this.unit && addUnit) {
        value2 = `${value2}${this.unit}`;
      }
    }
    return value2 != null ? value2 : "";
  }
  /**
   * Renderer that displays a formatted number in the cell. If you create a custom renderer, and want to include the
   * formatted number you can call `defaultRenderer` from it.
   *
   * ```javascript
   * new Grid({
   *     columns: [
   *         {
   *             type   : 'number',
   *             text   : 'Total cost',
   *             field  : 'totalCost',
   *             format : {
   *                 style    : 'currency',
   *                 currency : 'USD'
   *             },
   *             renderer({ value }) {
   *                  return `Total cost: ${this.defaultRenderer({ value })}`;
   *             }
   *         }
   *     ]
   * }
   * ```
   *
   * @param {Object} rendererData The data object passed to the renderer
   * @param {Number} rendererData.value The value to display
   * @returns {String} Formatted number
   * @typings {String|DomConfig}
   */
  defaultRenderer({ value: value2 }) {
    return this.formatValue(value2);
  }
  updateLocalization() {
    super.updateLocalization();
    this._formatter = null;
  }
};
//region Config
__publicField(NumberColumn, "type", "number");
// Type to use when auto adding field
__publicField(NumberColumn, "fieldType", "number");
__publicField(NumberColumn, "fields", [
  /**
   * The format to use for rendering numbers.
   *
   * By default, the locale's default number formatter is used. For `en-US`, the
   * locale default is a maximum of 3 decimal digits, using thousands-based grouping.
   * This would render the number `1234567.98765` as `'1,234,567.988'`.
   *
   * To round to whole integers, with a "," as a thousand delimiter:
   * ```javascript
   * format : '9,999.'
   * ```
   *
   * To display USD currency
   * ```javascript
   * format : {
   *     style    : 'currency',
   *     currency : 'USD',
   *     fraction : 0
   * }
   * ```
   * Read more about the formatting options at {@link Core/helper/util/NumberFormat}
   *
   * @config {String|NumberFormatConfig} format
   */
  { name: "format", defaultValue: "" },
  /**
   * The minimum value for the field used during editing.
   * @config {Number} min
   * @category Common
   */
  "min",
  /**
   * The maximum value for the field used during editing.
   * @config {Number} max
   * @category Common
   */
  "max",
  /**
   * Step size for the field used during editing. Also used when pressing up/down keys in the field.
   * @prp {Number} step
   * @category Common
   */
  "step",
  /**
   * Large step size, defaults to 10 * `step`. Applied when pressing SHIFT and stepping either by click or
   * when using the Up/Down keys.
   * @prp {Number} largeStep
   * @category Common
   */
  "largeStep",
  /**
   * Unit to append to displayed value.
   * @config {String} unit
   * @category Common
   */
  "unit",
  /**
   * Renderer function used to customize and style the number displayed in the cell. Return the cell text you
   * want to display. Can also affect other aspects of the cell, such as styling.
   *
   * <div class="note">
   * You should never modify any records inside this method.
   * </div>
   *
   * ```javascript
   * new Grid({
   *     columns : [
   *         { text : 'Temperature', type : 'number', renderer : ({ record, value }) => value + 'F' },
   *     ]
   * });
   * ```
   *
   * @config {Function} renderer
   * @param {Object} renderData Object containing renderer parameters
   * @param {HTMLElement} renderData.cellElement Cell element, for adding CSS classes, styling etc. Can be `null` in case of export
   * @param {Number|null|undefined} renderData.value The Number value to be displayed in the cell
   * @param {Core.data.Model} renderData.record Record for the row
   * @param {Grid.column.Column} renderData.column This column
   * @param {Grid.view.Grid} renderData.grid This grid
   * @param {Grid.row.Row} renderData.row Row object. Can be null in case of export.
   *   Use the {@link Grid.row.Row#function-assignCls row's API} to manipulate CSS class names.
   * @param {Object} renderData.size Set `size.height` to specify the desired row height for the current
   *   row. Largest specified height is used, falling back to configured {@link Grid/view/Grid#config-rowHeight}
   *   in case none is specified. Can be null in case of export
   * @param {Number} renderData.size.height Set this to request a certain row height
   * @param {Number} renderData.size.configuredHeight Row height that will be used if none is requested
   * @param {Boolean} renderData.isExport `true` if the record is being exported to Excel or a textual format, enabling special handling during export.
   * @param {Boolean} renderData.isMeasuring True if the column is being measured for a `resizeToFitContent` call.
   *   In which case an advanced renderer might need to take different actions.
   * @returns {String|DomConfig|DomConfig[]|null}
   *
   * @category Rendering
   */
  "renderer"
]);
__publicField(NumberColumn, "defaults", {
  filterType: "number",
  /**
   * Text align. Accepts `'left'`/`'center'`/`'right'` or direction neutral `'start'`/`'end'`
   * @config {'left'|'center'|'right'|'start'|'end'} align
   * @category Layout
   * @default 'end'
   */
  align: "end",
  exportedType: "number"
});
ColumnStore.registerColumnType(NumberColumn, true);
NumberColumn.exposeProperties();
NumberColumn._$name = "NumberColumn";

// ../Grid/lib/Grid/column/AggregateColumn.js
var AggregateColumn = class extends NumberColumn {
  static get defaults() {
    return {
      /**
       * Math Function name, or function name prepended by `"up."` that is resolvable in an
       * ancestor component (such as the owning Grid, or a height Container), or a function to
       * use to aggregate child record values for this column, or a function.
       *
       * This Column is provided with a `sum` and `avg` function. The default function is `sum`
       * which is used for the aggregation.
       *
       * The function is passed a set of child node values, each value in a separate argument
       * and should return a single value based upon the value set passed.
       * @config {'sum'|'avg'|'min'|'max'|Function}
       * @param {Core.data.Model[]} records Records for aggregation
       * @returns {*} Aggregated value
       * @category Common
       */
      function: "sum",
      /**
       * Set to `true` to include changes to parent (aggregate) rows in the store's modification tracking.
       * @config {Boolean} includeParentInChangeSet
       * @category Common
       */
      includeParentInChangeSet: null,
      /**
       * Set to `false` to disable aggregates being calculated.
       * @config {Boolean} enableAggregation
       * @category Common
       */
      enableAggregation: true,
      /**
       * Set to `true` to include filtered out records in the aggregation.
       * @prp {Boolean} includeFilteredOutRecords
       * @category Common
       */
      includeFilteredOutRecords: null
    };
  }
  construct(data, columnStore) {
    const me = this;
    me.configuredAlign = "align" in data;
    me.configuredEditor = "editor" in data;
    super.construct(...arguments);
    const { grid } = columnStore;
    if (me.function === "sum") {
      me.function = "sumChildren";
    }
    if (grid) {
      me.store = grid.store;
    }
  }
  set store(store) {
    const me = this, storeListeners = {
      update: "onRecordUpdate",
      thisObj: me,
      prio: 1e3
    }, oldStore = me._store;
    if (store !== oldStore) {
      if (oldStore) {
        oldStore.un(storeListeners);
      }
      me._store = store;
      const { modelClass } = store, field = modelClass.fieldMap[me.field];
      if (field && field.type === "number") {
        if (!me.configuredAlign) {
          me.align = "end";
        }
        if (!me.configuredEditor) {
          me.editor = "number";
        }
      }
      store.ion(storeListeners);
    }
  }
  canEdit(record) {
    return record.isLeaf && super.canEdit(record);
  }
  get store() {
    return this._store;
  }
  sumChildren(...args) {
    let result = 0;
    for (let i = 0, { length } = args; i < length; i++) {
      result += parseFloat(args[i] || 0, 10);
    }
    return result;
  }
  avg(...args) {
    let result = 0;
    const { length } = args;
    for (let i = 0; i < length; i++) {
      result += parseFloat(args[i] || 0, 10);
    }
    return result / length;
  }
  onRecordUpdate({ record, changes }) {
    const me = this, { rowManager } = me.grid;
    if (me.field in changes) {
      if (record.isLeaf) {
        record.bubble((rec) => {
          const row = rowManager.getRowFor(rec);
          if (row) {
            const cellElement = row.getCell(me.field);
            if (cellElement) {
              row.renderCell(cellElement);
            }
          }
        }, true);
      }
    }
  }
  getRawValue(record) {
    let value2;
    const me = this, { field, grid, enableAggregation } = me, children = grid.store.isFiltered && me.includeFilteredOutRecords ? record.unfilteredChildren : record.children;
    if (children == null ? void 0 : children.length) {
      const fn2 = me.function, isMathFn = typeof fn2 === "string" && typeof Math[fn2] === "function", {
        handler,
        thisObj
      } = isMathFn ? {
        handler: Math[fn2],
        thisObj: Math
      } : me.resolveCallback(fn2);
      value2 = enableAggregation ? handler.apply(thisObj, children.map((r) => me.getRawValue(r))) : void 0;
      if (me.includeParentInChangeSet && !grid.isTreeGrouped) {
        record.set(field, value2, true);
      } else {
        record.setData(field, value2);
      }
    } else {
      value2 = record.getValue(field);
    }
    return value2;
  }
  canFillValue({ record }) {
    return record.isLeaf && super.canFillValue(...arguments);
  }
};
//region Config
__publicField(AggregateColumn, "type", "aggregate");
__publicField(AggregateColumn, "fields", [
  "function",
  "includeParentInChangeSet",
  "enableAggregation",
  "includeFilteredOutRecords"
]);
ColumnStore.registerColumnType(AggregateColumn, true);
AggregateColumn.exposeProperties();
AggregateColumn._$name = "AggregateColumn";

// ../Grid/lib/Grid/column/WidgetColumn.js
var onWidgetChange = ({ source, value: value2, userAction }) => {
  if (userAction) {
    source.cellInfo.record.setValue(source.name, value2);
  }
};
var WidgetColumn = class extends Column {
  /**
   * A renderer function, which gives you access to render data like the current `record`, `cellElement` and the
   * {@link #config-widgets} of the column. See {@link #config-renderer}
   * for more information.
   *
   * ```javascript
   * new Grid({
   *     columns : [
   *         {
   *              type: 'check',
   *              field: 'allow',
   *              // In the column afterRenderCell callback, we get access to the record and column widgets
   *              afterRenderCell({ record, widgets }) {
   *                  // Hide checkboxes in certain rows
   *                  widgets[0].hidden = record.readOnly;
   *              }
   *         }
   *     ]
   * });
   * ```
   *
   * @config {Function} afterRenderCell
   * @param {Object} renderData Object containing renderer parameters
   * @param {HTMLElement} renderData.cellElement Cell element, for adding CSS classes, styling etc. Can be `null` in case of export
   * @param {*} renderData.value Value to be displayed in the cell
   * @param {Core.data.Model} renderData.record Record for the row
   * @param {Grid.column.Column} renderData.column This column
   * @param {Core.widget.Widget[]} renderData.widgets An array of the widgets rendered into this cell
   * @param {Grid.view.Grid} renderData.grid This grid
   * @param {Grid.row.Row} renderData.row Row object. Can be null in case of export.
   *   Use the {@link Grid.row.Row#function-assignCls row's API} to manipulate CSS class names.
   * @param {Object} renderData.size Set `size.height` to specify the desired row height for the current row.
   *   Largest specified height is used, falling back to configured {@link Grid/view/Grid#config-rowHeight}
   *   in case none is specified. Can be null in case of export
   * @param {Number} renderData.size.height Set this to request a certain row height
   * @param {Number} renderData.size.configuredHeight Row height that will be used if none is requested
   * @param {Boolean} renderData.isExport `true` if the record is being exported to Excel or a textual format, enabling special handling during export.
   * @param {Boolean} renderData.isTreeGroup True if record is a generated tree group parent record
   * @param {Boolean} renderData.isMeasuring True if the column is being measured for a `resizeToFitContent` call.
   *   In which case an advanced renderer might need to take different actions.
   * @returns {void}
   *
   * @category Rendering
   */
  /**
   * A renderer function, which gives you access to render data like the current `record`, `cellElement` and the
   * {@link #config-widgets} of the column. See {@link #config-renderer}
   * for more information.
   *
   * ```javascript
   * new Grid({
   *     columns : [
   *         {
   *              type: 'check',
   *              field: 'allow',
   *              // In the column afterRenderCell callback, we get access to the record and column widgets
   *              afterRenderCell({ record, widgets }) {
   *                  // Hide checkboxes in certain rows
   *                  widgets[0].hidden = record.readOnly;
   *              }
   *         }
   *     ]
   * });
   * ```
   *
   * @config {Function} renderer
   * @deprecated 6.0 In favor of using `afterRenderCell`
   * @param {Object} renderData Object containing renderer parameters
   * @param {HTMLElement} renderData.cellElement Cell element, for adding CSS classes, styling etc. Can be `null` in case of export
   * @param {*} renderData.value Value to be displayed in the cell
   * @param {Core.data.Model} renderData.record Record for the row
   * @param {Grid.column.Column} renderData.column This column
   * @param {Core.widget.Widget[]} renderData.widgets An array of the widgets rendered into this cell
   * @param {Grid.view.Grid} renderData.grid This grid
   * @param {Grid.row.Row} renderData.row Row object. Can be null in case of export.
   *   Use the {@link Grid.row.Row#function-assignCls row's API} to manipulate CSS class names.
   * @param {Object} renderData.size Set `size.height` to specify the desired row height for the current row.
   *   Largest specified height is used, falling back to configured {@link Grid/view/Grid#config-rowHeight}
   *   in case none is specified. Can be null in case of export
   * @param {Number} renderData.size.height Set this to request a certain row height
   * @param {Number} renderData.size.configuredHeight Row height that will be used if none is requested
   * @param {Boolean} renderData.isExport `true` if the record is being exported to Excel or a textual format, enabling special handling during export.
   * @param {Boolean} renderData.isTreeGroup True if record is a generated tree group parent record
   * @param {Boolean} renderData.isMeasuring True if the column is being measured for a `resizeToFitContent` call.
   *   In which case an advanced renderer might need to take different actions.
   * @returns {void}
   *
   * @category Rendering
   */
  static get defaults() {
    return {
      filterable: false,
      sortable: false,
      editor: false,
      searchable: false,
      fitMode: false,
      alwaysClearCell: false,
      ariaLabel: "L{WidgetColumn.rowWidgets}"
    };
  }
  //endregion
  //region Init / Destroy
  construct(config) {
    if (!config.disableRendererWarning) {
      if (config.renderer) {
        config.afterRenderCell = config.renderer;
        delete config.renderer;
        VersionHelper.deprecate("grid", "7.0.0", "Using deprecated `renderer` with WidgetColumn, use `afterRenderCell` callback instead.");
      }
      if (this.renderer && this.renderer !== this.defaultRenderer) {
        config.afterRenderCell = this.renderer;
        this.renderer = this.defaultRenderer;
        VersionHelper.deprecate("grid", "7.0.0", "Using deprecated `renderer` with WidgetColumn, use `afterRenderCell` callback instead.");
      }
    }
    super.construct(...arguments);
    const me = this, { grid } = me;
    if (grid) {
      if (grid.widgetColumnCache) {
        me.widgetColumnCache = grid.widgetColumnCache;
      } else {
        me.widgetColumnCache = grid.widgetColumnCache = /* @__PURE__ */ new Map();
        grid.ion({
          removeRows: "onRemoveGridRows",
          thisObj: this
        });
      }
    }
  }
  onRemoveGridRows({ rows }) {
    rows.forEach(({ columnWidgets }) => {
      if (columnWidgets) {
        this.freeWidgets([...columnWidgets.values()].flat());
        columnWidgets.clear();
      }
    });
  }
  freeWidgets(widgets) {
    widgets == null ? void 0 : widgets.forEach((widget) => {
      var _a5;
      const { widgetColumnCache } = this, { column } = widget.cellInfo, key = `${column.id}-${widget.widgetColumnIndex}`;
      (_a5 = column.onAfterWidgetDerendered) == null ? void 0 : _a5.call(column, widget);
      widget.cellInfo = widget.__boundRecordId = widget.owner = null;
      widget.element.remove();
      (widgetColumnCache.get(key) || widgetColumnCache.set(key, []).get(key)).push(widget);
    });
    widgets.length = 0;
  }
  freeColumnWidgets() {
    const { id: id2 } = this;
    this.grid.rowManager.forEach((row) => {
      var _a5;
      const widgets = (_a5 = row.columnWidgets) == null ? void 0 : _a5.get(id2);
      if (widgets == null ? void 0 : widgets.length) {
        this.freeWidgets(widgets);
      }
    });
  }
  hide() {
    this.freeColumnWidgets();
    super.hide(...arguments);
  }
  getWidget(widgetConfig, renderData) {
    var _a5;
    const me = this, {
      grid,
      widgetColumnCache
    } = me, key = `${renderData.column.id}-${widgetConfig.widgetColumnIndex}`, widgets = widgetColumnCache.get(key) || widgetColumnCache.set(key, []).get(key);
    let result, { length } = widgets;
    if (length) {
      result = widgets[--length];
      delete result.onBeforeDestroy;
      widgets.length = length;
      widgetConfig.owner = grid;
      Widget.reconfigure(result, widgetConfig);
    } else {
      me.onBeforeWidgetCreate(widgetConfig, renderData);
      widgetConfig.recomposeAsync = false;
      widgetConfig.owner = grid;
      result = Widget.create(widgetConfig);
      me.onAfterWidgetCreate(result, renderData);
      if (result.name) {
        result.ion({ change: onWidgetChange });
      }
    }
    (_a5 = me.onBeforeWidgetRender) == null ? void 0 : _a5.call(me, result);
    return result;
  }
  doDestroy() {
    if (this.grid.isDestroying) {
      [...this.widgetColumnCache.values()].flat().forEach((widget) => widget.destroy());
      this.widgetColumnCache.clear();
    } else {
      this.freeColumnWidgets();
    }
    super.doDestroy();
  }
  // Called by grid when its read-only state is toggled
  updateReadOnly(readOnly) {
    if (this.readOnly) {
      return;
    }
    this.grid.rowManager.forEach((row) => {
      var _a5, _b2;
      (_b2 = (_a5 = row.columnWidgets) == null ? void 0 : _a5.get(this.id)) == null ? void 0 : _b2.forEach((widget) => {
        if (!widget.cellInfo.record.readOnly) {
          widget.readOnly = readOnly;
        }
      });
    });
  }
  //endregion
  //region Render
  /**
   * Renderer that displays a widget in the cell.
   * @param {Object} renderData Render data
   * @param {Grid.column.Column} renderData.column Rendered column
   * @param {Core.data.Model} renderData.record Rendered record
   * @private
   */
  defaultRenderer(renderData) {
    const me = this, { grid } = me, {
      cellElement,
      column,
      record,
      row,
      isExport
    } = renderData, { widgets } = column;
    if (!isExport && widgets) {
      const columnWidgets = row.columnWidgets || (row.columnWidgets = /* @__PURE__ */ new Map()), cellWidgets = columnWidgets.get(me.id);
      [...cellElement.childNodes].forEach((e) => {
        var _a5;
        if (!((_a5 = e.classList) == null ? void 0 : _a5.contains("b-widget"))) {
          e.remove();
        }
      });
      renderData.widgets = column.widgets.map((widgetCfg, i) => {
        var _a5, _b2;
        widgetCfg.widgetColumnIndex = i;
        const widget = (cellWidgets == null ? void 0 : cellWidgets[i]) || me.getWidget(widgetCfg, renderData);
        widget.cellInfo = renderData;
        if (!widget.element.isConnected) {
          if (widget.rendered) {
            cellElement.appendChild(widget.element);
          } else {
            widget.render(cellElement);
          }
        }
        if (!me.meta.isSelectionColumn) {
          widget.readOnly = grid.readOnly || record.readOnly || me.readOnly;
        }
        if (((_a5 = me.onBeforeWidgetSetValue) == null ? void 0 : _a5.call(me, widget, renderData)) !== false) {
          const valueProperty = widgetCfg.valueProperty || "value" in widget && "value" || widget.defaultBindProperty;
          if (valueProperty && (!column.noFieldSpecified && column.widgets.length === 1 || widget.name)) {
            const value2 = widget.name ? record.getValue(widget.name) : renderData.value;
            if (typeof value2 == "object" && widget.__boundRecordId !== record.id) {
              widget[`_${valueProperty}`] = NaN;
            }
            widget.suspendEvents();
            widget[valueProperty] = value2;
            widget.resumeEvents();
            widget.__boundRecordId = record.id;
          }
        }
        (_b2 = me.onAfterWidgetSetValue) == null ? void 0 : _b2.call(me, widget, renderData);
        return widget;
      });
      columnWidgets.set(me.id, renderData.widgets);
    }
    if (!widgets) {
      return "";
    }
  }
  //endregion
  //region Other
  /**
   * Called before widget is created on rendering
   * @param {ContainerItemConfig} widgetCfg Widget config
   * @param {Object} renderData Render data
   * @private
   */
  onBeforeWidgetCreate(widgetCfg, renderData) {
  }
  /**
   * Called after widget is created on rendering
   * @param {Core.widget.Widget} widget Created widget
   * @param {Object} renderData Render data
   * @private
   */
  onAfterWidgetCreate(widget, renderData) {
  }
  /**
   * Called before the widget gets its value on rendering. Pass `false` to skip value setting while rendering
   * @preventable
   * @function onBeforeWidgetSetValue
   * @param {Core.widget.Widget} widget Created widget
   * @param {Object} renderData Render data
   * @param {Grid.column.Column} renderData.column Rendered column
   * @param {Core.data.Model} renderData.record Rendered record
   */
  /**
   * Called after the widget gets its value on rendering.
   * @function onAfterWidgetSetValue
   * @param {Core.widget.Widget} widget Created widget
   * @param {Object} renderData Render data
   * @param {Grid.column.Column} renderData.column Rendered column
   * @param {Core.data.Model} renderData.record Rendered record
   */
  // Null implementation because there is no way of ascertaining whether the widgets get their width from
  // the column, or the column shrinkwraps the Widget.
  // Remember that the widget could have a width from a CSS rule which we cannot read.
  // It might have width: 100%, or a flex which would mean it is sized by us, but we cannot read that -
  // getComputedStyle would return the numeric width.
  resizeToFitContent() {
  }
  //endregion
};
//region Config
__publicField(WidgetColumn, "$name", "WidgetColumn");
__publicField(WidgetColumn, "type", "widget");
__publicField(WidgetColumn, "fields", [
  /**
   * An array of {@link Core.widget.Widget} config objects
   * @config {ContainerItemConfig[]} widgets
   * @category Common
   */
  "widgets",
  "disableRendererWarning"
]);
ColumnStore.registerColumnType(WidgetColumn);
WidgetColumn.exposeProperties();
WidgetColumn._$name = "WidgetColumn";

// ../Grid/lib/Grid/column/CheckColumn.js
var CheckColumn = class extends WidgetColumn {
  construct(config, store) {
    var _a5;
    config.localizableProperties = Config.mergeMethods.distinct(config.localizableProperties, ["checkAriaLabel"]);
    super.construct(...arguments);
    const me = this;
    me.widgets[0].cls = me.checkCls;
    Object.assign(me, {
      externalHeaderRenderer: me.headerRenderer,
      externalOnBeforeWidgetSetValue: me.onBeforeWidgetSetValue,
      externalOnAfterWidgetSetValue: me.onAfterWidgetSetValue
    });
    me.setData({
      onBeforeWidgetSetValue: me.internalOnBeforeWidgetSetValue,
      onAfterWidgetSetValue: me.internalOnAfterWidgetSetValue,
      headerRenderer: me.internalHeaderRenderer
    });
    if (!me.meta.isSelectionColumn) {
      const modelClass = (_a5 = me.grid) == null ? void 0 : _a5.store.modelClass;
      if (!me.field) {
        console.warn("CheckColumn MUST be configured with a field, otherwise the checked state will not be persistent. Widgets are recycled and reused");
      } else if (modelClass && typeof me[me.field] !== "boolean" && !modelClass.fieldMap[me.field] && !me.constructor.suppressNoModelFieldWarning && !config.suppressNoModelFieldWarning) {
        console.warn(me.$$name + " is configured with a field, but this is not part of your Model `fields` collection.");
        modelClass.addField({ name: me.field, type: "boolean" });
      }
    }
  }
  get groupHeaderReserved() {
    return this.grid.selectionMode.showCheckAllInGroupRows;
  }
  doDestroy() {
    var _a5;
    (_a5 = this.headerCheckbox) == null ? void 0 : _a5.destroy();
    super.doDestroy();
  }
  internalHeaderRenderer({ headerElement, column }) {
    let returnValue;
    headerElement.classList.add("b-check-header");
    if (column.showCheckAll) {
      headerElement.classList.add("b-check-header-with-checkbox");
      if (column.headerCheckbox) {
        headerElement.appendChild(column.headerCheckbox.element);
      } else {
        column.headerCheckbox = new Checkbox({
          appendTo: headerElement,
          owner: this.grid,
          ariaLabel: "L{Checkbox.toggleSelection}",
          internalListeners: {
            change: "onCheckAllChange",
            thisObj: column
          }
        });
      }
    } else {
      returnValue = column.headerText;
    }
    returnValue = column.externalHeaderRenderer ? column.externalHeaderRenderer.call(this, ...arguments) : returnValue;
    return column.showCheckAll ? void 0 : returnValue;
  }
  updateCheckAllState(value2) {
    if (this.headerCheckbox) {
      this.headerCheckbox.suspendEvents();
      this.headerCheckbox.checked = value2;
      this.headerCheckbox.resumeEvents();
    }
  }
  onCheckAllChange({ checked }) {
    const me = this;
    if (me.field) {
      const { store } = me.grid;
      store.beginBatch();
      store.forEach((record) => me.updateRecord(record, me.field, checked));
      store.endBatch();
    }
    me.trigger("toggleAll", { checked });
  }
  //endregion
  defaultRenderer(renderData) {
    var _a5;
    const {
      value: value2,
      isExport,
      record
    } = renderData, { grid } = this, result = super.defaultRenderer(renderData), checkbox = (_a5 = renderData.widgets) == null ? void 0 : _a5[0];
    if (isExport) {
      return result != null ? result : value2 == null ? "" : value2;
    }
    checkbox.readOnly = this.readOnly || (grid.readOnly || record.readOnly) && !this.meta.isSelectionColumn;
    if (value2) {
      checkbox.input.setAttribute("checked", true);
    } else {
      checkbox.input.removeAttribute("checked");
    }
    checkbox.input.setAttribute("aria-label", this.checkAriaLabel);
    return result;
  }
  //region Widget rendering
  onBeforeWidgetRender(widget, event) {
    widget.ion({
      beforeChange: "onBeforeCheckboxChange",
      change: "onCheckboxChange",
      thisObj: this
    });
  }
  onAfterWidgetDerendered(widget, event) {
    widget.un({
      beforeChange: "onBeforeCheckboxChange",
      change: "onCheckboxChange",
      thisObj: this
    });
  }
  internalOnBeforeWidgetSetValue(widget, { cellElement }) {
    var _a5;
    cellElement.widget = widget;
    widget.record = widget.cellInfo.record;
    (_a5 = this.externalOnBeforeWidgetSetValue) == null ? void 0 : _a5.call(this, ...arguments);
  }
  internalOnAfterWidgetSetValue(widget) {
    var _a5;
    (_a5 = this.externalOnAfterWidgetSetValue) == null ? void 0 : _a5.call(this, ...arguments);
  }
  //endregion
  //region Events
  onBeforeCheckboxChange({ source, checked, userAction }) {
    const me = this, { grid } = me, { record } = source.cellInfo;
    if (userAction && me.field && (!grid.features.cellEdit || grid.features.cellEdit.disabled) || me.meta.isSelectionColumn && !grid.isSelectable(record) && (!grid.selectionMode.showCheckAllInGroupRows || !record.isGroupHeader) && checked) {
      return false;
    }
    return me.trigger("beforeToggle", { record, checked });
  }
  onCheckboxChange({ source, checked }) {
    const me = this, { record } = source.cellInfo, { field } = me;
    if (field) {
      me.updateRecord(record, field, checked);
      if (checked) {
        me.updateCheckAllState(me.grid.store.every((r) => r[field], null, true));
      } else {
        me.updateCheckAllState(false);
      }
    }
    me.trigger("toggle", { record, checked, checkbox: source });
  }
  updateRecord(record, field, checked) {
    record.setValue(field, checked);
  }
  //endregion
  onCellKeyDown({ event, cellElement, record }) {
    if (event.key === " ") {
      const { target } = event, checkbox = cellElement.widget;
      if (target.matches(".b-grid-cell") && !((record == null ? void 0 : record.readOnly) || checkbox.disabled || checkbox.readOnly)) {
        checkbox == null ? void 0 : checkbox.toggle();
        event.preventDefault();
        event.handled = true;
      }
    }
  }
  // This function is not meant to be called by any code other than Base#getCurrentConfig().
  // It extracts the current configs (fields) for the column, with special handling for the hooks
  getCurrentConfig(options) {
    const result = super.getCurrentConfig(options);
    delete result.onBeforeWidgetSetValue;
    delete result.onAfterWidgetSetValue;
    if (this.externalOnBeforeWidgetSetValue) {
      result.onBeforeWidgetSetValue = this.externalOnBeforeWidgetSetValue;
    }
    if (this.externalOnAfterWidgetSetValue) {
      result.onAfterWidgetSetValue = this.externalOnAfterWidgetSetValue;
    }
    return result;
  }
};
//region Config
__publicField(CheckColumn, "$name", "CheckColumn");
__publicField(CheckColumn, "type", "check");
__publicField(CheckColumn, "fields", [
  "checkCls",
  /**
   * The `aria-label` to use for the default checkbox widget of cells in this Column
   * @config {String} checkAriaLabel
   * @category Accessibility
   */
  {
    name: "checkAriaLabel",
    defaultValue: "L{Checkbox.toggleName}"
  },
  "showCheckAll",
  "onAfterWidgetSetValue",
  "onBeforeWidgetSetValue",
  "callOnFunctions",
  "onBeforeToggle",
  "onToggle",
  "onToggleAll"
]);
__publicField(CheckColumn, "defaults", {
  align: "center",
  /**
   * CSS class name to add to checkbox
   * @config {String}
   * @category Rendering
   */
  checkCls: null,
  /**
   * True to show a checkbox in the column header to be able to select/deselect all rows
   * @config {Boolean}
   */
  showCheckAll: false,
  sortable: true,
  filterable: true,
  /**
   * The minimum column width. If value is a `Number`, then the width is in pixels
   * @config {Number|String} minWidth
   * @default 30
   * @category Layout
   */
  minWidth: 30,
  widgets: [{
    type: "checkbox",
    valueProperty: "checked"
  }]
});
ColumnStore.registerColumnType(CheckColumn, true);
CheckColumn._$name = "CheckColumn";

// ../Grid/lib/Grid/column/ColorColumn.js
var ColorColumn = class extends Column {
  constructor() {
    super(...arguments);
    __publicField(this, "preventCellEditViaMouse", true);
  }
  construct() {
    super.construct(...arguments);
    const me = this, { editor, colors } = me;
    if (colors == null ? void 0 : colors.length) {
      editor.colors = colors;
    }
    editor.addNoColorItem = me.addNoColorItem;
    editor.ion({ colorSelected: "onColorSelected", thisObj: me });
  }
  applyValue(useProp, field, value2) {
    if (!this.isConstructing) {
      const { editor } = this;
      if (field === "colors") {
        editor.colors = value2;
      } else if (field === "addNoColorItem") {
        editor.addNoColorItem = value2;
      }
    }
    super.applyValue(...arguments);
  }
  get picker() {
    return this.editor.picker;
  }
  defaultRenderer({ value: value2 }) {
    let colorClass = "b-empty", backgroundColor = value2;
    if (value2) {
      const colorClassName = this.picker.getColorClassName(value2);
      if (colorClassName) {
        colorClass = colorClassName;
        backgroundColor = null;
      } else {
        colorClass = "";
      }
    }
    return {
      className: "b-color-cell-inner " + colorClass,
      style: {
        backgroundColor
      },
      "data-btip": value2
    };
  }
  // Handles single click edit on the color box
  // ColorColumn didn't have an editor previously, but now do, so this handles non-celledit edit
  onCellClick({ grid, record, target }) {
    var _a5;
    if (target.classList.contains("b-color-cell-inner") && !this.readOnly && !grid.readOnly && !record.isSpecialRow && !record.readOnly && !((_a5 = grid.features.cellEdit) == null ? void 0 : _a5.finishEditingPromise)) {
      const { picker } = this, value2 = record.get(this.field);
      this._editingRecord = record;
      picker.deselectAll();
      picker.select(value2);
      picker.refresh();
      picker.showBy(target);
    }
  }
  onColorSelected({ color }) {
    var _a5;
    if (!((_a5 = this.grid.features.cellEdit) == null ? void 0 : _a5.isEditing) && this._editingRecord) {
      this._editingRecord.set(this.field, color);
    }
  }
};
__publicField(ColorColumn, "$name", "ColorColumn");
__publicField(ColorColumn, "type", "color");
__publicField(ColorColumn, "fields", [
  /**
   * The `colors` property can be an array of string CSS colors or of objects with `color`
   * and `text` properties from which the user can chose from. This will override the
   * {@link Core.widget.ColorPicker#config-colors pickers default colors}.
   *
   * Provide an array of string CSS colors:
   * ```javascript
   * new Grid({
   *    columns : [
   *       {
   *          type   : 'color',
   *          field  : 'color',
   *          text   : 'Color',
   *          colors : ['#00FFFF', '#F0FFFF', '#89CFF0', '#0000FF', '#7393B3']
   *       }
   *    ]
   * });
   * ```
   * @prp {Array<String|ColorDescriptor>} colors
   */
  "colors",
  /**
   * Adds an option in the picker to set no background color
   * @prp {Boolean} addNoColorItem
   * @default true
   */
  { name: "addNoColorItem", defaultValue: true }
]);
/**
 * @hideconfigs editor, cellEditor
 */
__publicField(ColorColumn, "defaults", {
  align: "center",
  editor: "colorfield"
});
ColumnStore.registerColumnType(ColorColumn);
ColorColumn._$name = "ColorColumn";

// ../Grid/lib/Grid/column/DateColumn.js
var DateColumn = class extends Column {
  static get defaults() {
    return {
      /**
       * Min value for the cell editor
       * @config {String|Date} min
       */
      /**
       * Max value for the cell editor
       * @config {String|Date} max
       */
      /**
       * The {@link Core.data.field.DateDataField#config-name} of the data model date field to read data from.
       * @config {String} field
       * @category Common
       */
      /**
       * Date format to convert a given date object into a string to display. By default `L` format is used, which
       * contains the following info: full year, 2-digit month, and 2-digit day. Depending on the browser locale,
       * the formatted date might look different. [Intl.DateTimeFormat API](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat)
       * is used to format the date. Here is an example of possible outputs depending on the browser locale:
       *
       * ```javascript
       * // These options represent `L` format
       * const options = { year : 'numeric', month : '2-digit', day : '2-digit' };
       *
       * new Intl.DateTimeFormat('en-US', options).format(new Date(2021, 6, 1)); // "07/01/2021"
       * new Intl.DateTimeFormat('ru-RU', options).format(new Date(2021, 6, 1)); // "01.07.2021"
       *
       * // Formatting using Bryntum API
       * LocaleManager.applyLocale('En');
       * DateHelper.format(new Date(2021, 6, 1), 'L'); // "07/01/2021"
       * LocaleManager.applyLocale('Ru');
       * DateHelper.format(new Date(2021, 6, 1), 'L'); // "01.07.2021"
       * ```
       *
       * To learn more about available formats check out {@link Core.helper.DateHelper} docs.
       *
       * Note, the {@link Core.data.field.DateDataField field} this column reads data from should be a type of date.
       * @config {String}
       * @default
       * @category Common
       */
      format: "L",
      /**
       * Time increment duration value to apply when clicking the left / right trigger icons. See
       * {@link Core.widget.DateField#config-step} for more information
       * Set to `null` to hide the step triggers.
       * @config {String|Number|DurationConfig}
       * @default
       * @category Common
       */
      step: 1,
      minWidth: 85,
      filterType: "date"
    };
  }
  construct(config = {}, columnStore) {
    super.construct(config, columnStore);
    if (this.grid) {
      const { field } = this, { store } = this.grid, { modelClass } = store, fieldInstance = field && modelClass.getFieldDefinition(field);
      if (fieldInstance && !fieldInstance.isDateDataField) {
        console.warn(`DateColumn expects the \`type\` of the model field \`${field}\` to be of type \`date\``);
        const dateField = modelClass.addField({
          ...fieldInstance.getCurrentConfig(),
          type: "date",
          ["format" in config ? "format" : void 0]: this.format
        });
        store.forEach((rec) => rec.data[fieldInstance.dataSource] = dateField.convert(rec[field]));
      }
    }
  }
  //endregion
  //region Display
  /**
   * Renderer that displays the date with the specified format. Also adds cls 'date-cell' to the cell.
   * @private
   */
  defaultRenderer({ value: value2 }) {
    return value2 ? this.formatValue(value2) : "";
  }
  /**
   * Group renderer that displays the date with the specified format.
   * @private
   */
  groupRenderer({ cellElement, groupRowFor }) {
    cellElement.innerHTML = this.formatValue(groupRowFor);
  }
  //endregion
  //region Formatter
  /**
   * Used by both renderer and groupRenderer to do the actual formatting of the date
   * @private
   * @param value
   * @returns {String}
   */
  formatValue(value2) {
    if (typeof value2 === "string") {
      value2 = DateHelper.parse(value2, this.format || void 0);
    }
    return DateHelper.format(value2, this.format || void 0);
  }
  //endregion
  //region Getters/setters
  /**
   * Get/Set format for date displayed in cell and editor (see {@link Core.helper.DateHelper#function-format-static} for formatting options)
   * @property {String}
   */
  set format(value2) {
    const { editor } = this.data;
    this.set("format", value2);
    if (editor) {
      editor.format = value2;
    }
  }
  get format() {
    return this.get("format");
  }
  get defaultEditor() {
    const me = this, { min: min4, max: max3, step, format: format2 } = me;
    return {
      name: me.field,
      type: "date",
      calendarContainerCls: "b-grid-cell-editor-related",
      weekStartDay: me.grid.weekStartDay,
      format: format2,
      max: max3,
      min: min4,
      step
    };
  }
  //endregion
};
//region Config
__publicField(DateColumn, "$name", "DateColumn");
__publicField(DateColumn, "type", "date");
// Type to use when auto adding field
__publicField(DateColumn, "fieldType", "date");
__publicField(DateColumn, "fields", [
  "format",
  "pickerFormat",
  "step",
  "min",
  "max",
  /**
   * Renderer function, used to style the date displayed in the cell. Can also affect other aspects of
   * the cell, such as styling.
   *
   * <div class="note">
   * You should never modify any records inside this method.
   * </div>
   *
   * ```javascript
   * new Grid({
   *     columns : [
   *         { type : 'date', text : 'Date of birth', field : 'dob' },
   *     ]
   * });
   * ```
   *
   * You can modify the row element too from inside a renderer to add custom CSS classes:
   *
   * ```javascript
   * new Grid({
   *     columns : [
   *         {
   *             type : 'date',
   *             text : 'Date of birth',
   *             field : 'dob',
   *             renderer : ({ record, row }) => {
   *                // Add special CSS class to new rows that have not yet been saved
   *               row.cls.newRow = record.isPhantom;
   *
   *               return record.dob;
   *         }
   *     ]
   * });
   * ```
   *
   * @config {Function} renderer
   * @param {Object} renderData Object containing renderer parameters
   * @param {HTMLElement} renderData.cellElement Cell element, for adding CSS classes, styling etc. Can be `null` in case of export
   * @param {Date|null|undefined} renderData.value The Date value to be displayed in the cell
   * @param {Core.data.Model} renderData.record Record for the row
   * @param {Grid.column.Column} renderData.column This column
   * @param {Grid.view.Grid} renderData.grid This grid
   * @param {Grid.row.Row} renderData.row Row object. Can be null in case of export.
   *   Use the {@link Grid.row.Row#function-assignCls row's API} to manipulate CSS class names.
   * @param {Object} renderData.size Set `size.height` to specify the desired row height for the current
   *   row. Largest specified height is used, falling back to configured {@link Grid/view/Grid#config-rowHeight}
   *   in case none is specified. Can be null in case of export
   * @param {Number} renderData.size.height Set this to request a certain row height
   * @param {Number} renderData.size.configuredHeight Row height that will be used if none is requested
   * @param {Boolean} renderData.isExport `true` if the record is being exported to Excel or a textual format, enabling special handling during export.
   * @param {Boolean} renderData.isMeasuring True if the column is being measured for a `resizeToFitContent` call.
   *   In which case an advanced renderer might need to take different actions.
   * @returns {String|DomConfig|DomConfig[]|null}
   *
   * @category Rendering
   */
  "renderer"
]);
ColumnStore.registerColumnType(DateColumn, true);
DateColumn.exposeProperties();
DateColumn._$name = "DateColumn";

// ../Grid/lib/Grid/column/PercentColumn.js
var PercentColumn = class extends NumberColumn {
  constructor() {
    super(...arguments);
    __publicField(this, "circleHeightPercentage", 0.75);
  }
  construct() {
    super.construct(...arguments);
    if (this.showCircle) {
      VersionHelper.deprecate("core", "7.0.0", "PercentColumn `showCircle` is deprecated, in favor of `mode`");
      this.mode = "circle";
    }
  }
  /**
   * Renderer that displays a progress bar in the cell. If you create a custom renderer, and want to include the
   * default markup you can call `defaultRenderer` from it.
   *
   * ```javascript
   * new Grid({
   *     columns: [
   *         {
   *             type: 'percent',
   *             text : 'Percent',
   *             field : 'percent',
   *             renderer({ value }) {
   *                 const domConfig = this.defaultRenderer();
   *
   *                 if (value > 100) {
   *                     domConfig.className = b-percent-bar-outer over-allocated';
   *                 }
   *
   *                 return domConfig;
   *             }
   *         }
   *     ]
   * }
   * ```
   *
   * @param {Object} rendererData The data object passed to the renderer
   * @param {Number} rendererData.value The value to display
   * @returns {DomConfig} DomConfig object representing the default markup for the cells content
   */
  defaultRenderer({ value: value2, record, isExport }) {
    var _a5, _b2;
    value2 = value2 || 0;
    const me = this, formattedValue = me.formatValue(value2, record) || value2, label = `${me.text ? `${me.text} : ` : ""}${formattedValue}`;
    if (isExport) {
      return formattedValue;
    } else if (me.mode === "number") {
      return formattedValue + "%";
    }
    const { min: min4, max: max3 } = me;
    if (me.mode === "circle") {
      const sizePx = Math.round(me.circleHeightPercentage * me.grid.rowHeight) + "px";
      return {
        tabIndex: 0,
        role: "progressbar",
        "aria-valuemin": min4,
        "aria-valuemax": max3,
        "aria-valuenow": value2,
        "aria-label": ((_a5 = me.formatAriaLabel) == null ? void 0 : _a5.call(me, label, record)) || label,
        className: {
          "b-percentdone-circle": 1,
          "b-empty": value2 === 0,
          "b-full": value2 === 100,
          "b-over": value2 > 100
        },
        style: {
          height: sizePx,
          width: sizePx,
          "--grid-percent-circle-angle": `${value2 / 100}turn`
        },
        dataset: {
          value: formattedValue
        }
      };
    }
    return {
      className: "b-percent-bar-outer",
      role: "progressbar",
      "aria-valuemin": min4,
      "aria-valuemax": max3,
      "aria-valuenow": value2,
      "aria-label": ((_b2 = me.formatAriaLabel) == null ? void 0 : _b2.call(me, label, record)) || label,
      tabIndex: 0,
      children: [
        {
          tag: "div",
          className: {
            "b-percent-bar": 1,
            "b-zero": value2 === 0,
            "b-low": value2 < me.lowThreshold,
            "b-full": value2 === 100,
            "b-over": value2 > 100
          },
          style: {
            width: value2 + "%"
          },
          children: [
            me.showValue ? {
              tag: "span",
              text: formattedValue + "%"
            } : void 0
          ]
        }
      ]
    };
  }
  // Null implementation for graphic representations, because the column width drives the width of its content.
  // So the concept of sizing to content is invalid here.
  resizeToFitContent() {
    if (this.mode === "number") {
      return super.resizeToFitContent(...arguments);
    }
  }
  get align() {
    const align = this.get("align"), { mode } = this;
    if (align) {
      return align;
    }
    if (mode === "number") {
      return "end";
    }
    if (mode === "circle") {
      return "center";
    }
    return "start";
  }
  set align(align) {
    this.set("align", align);
  }
  get htmlEncode() {
    return this.mode === "number";
  }
  get internalCellCls() {
    return this.mode === "bar" ? "b-percent-bar-cell" : "";
  }
};
__publicField(PercentColumn, "type", "percent");
__publicField(PercentColumn, "fields", [
  /**
   * @hideconfigs htmlEncode
   */
  "showValue",
  "lowThreshold",
  "showCircle",
  "mode"
]);
__publicField(PercentColumn, "defaults", {
  min: 0,
  max: 100,
  step: 1,
  align: null,
  /**
   * Set to `true` to render a circular progress bar instead of a progress bar
   * @config {Boolean} showCircle
   * @deprecated 6.0 In favor of `mode`
   * @category Rendering
   */
  showCircle: false,
  /**
   * This mode controls the rendering.
   * - `number` to render a formatted number value (e.g. 15%)
   * - `circle` to render a circular progress bar
   * - `bar` to render a plain rectangular progress bar
   * @config {'number'|'bar'|'circle'}
   * @default
   * @category Rendering
   */
  mode: "bar",
  /**
   * Set to `true` to render the number value inside the bar, for example `'15%'`.
   * @config {Boolean}
   * @default
   * @category Rendering
   */
  showValue: false,
  /**
   * When below this percentage the bar will have `b-low` CSS class added. By default, it turns the bar red.
   * @config {Number}
   * @default
   * @category Rendering
   */
  lowThreshold: 0,
  htmlEncode: false,
  searchable: false,
  summaryRenderer: ({ sum }) => `${sum}%`,
  fitMode: false
});
PercentColumn.sum = "average";
ColumnStore.registerColumnType(PercentColumn, true);
PercentColumn._$name = "PercentColumn";

// ../Grid/lib/Grid/column/RatingColumn.js
var RatingColumn = class extends NumberColumn {
  constructor() {
    super(...arguments);
    __publicField(this, "preventCellEditViaMouse", true);
  }
  static get defaults() {
    return {
      min: 0,
      max: 5,
      /**
       * The empty rating icon to show
       * @prp {String}
       * @category Rendering
       */
      emptyIcon: "b-icon b-icon-star",
      /**
       * The filled rating icon to show
       * @prp {String}
       * @category Rendering
       */
      filledIcon: "b-icon b-icon-star",
      /**
       * Allow user to click a rating icon to change the value
       * @config {Boolean}
       * @category Interaction
       */
      editable: true,
      exportedType: "string",
      filterType: "number",
      searchable: false,
      width: "11.2em",
      htmlEncode: false,
      minWidth: "11.2em",
      align: "start",
      fitMode: "value"
    };
  }
  /**
   * Renderer that displays a number of stars in the cell. Also adds CSS class 'b-rating-cell' to the cell.
   * @private
   */
  defaultRenderer({ value: value2, isExport }) {
    if (isExport) {
      return "\u2605".repeat(value2);
    }
    return {
      className: {
        "b-rating-cell-inner": 1,
        "b-not-editable": !this.editable
      },
      children: ArrayHelper.populate(this.max, (i) => {
        const filled = i < value2;
        return {
          tag: "i",
          className: {
            "b-rating-icon": true,
            "b-filled": filled,
            "b-empty": !filled,
            [filled ? this.filledIcon : this.emptyIcon]: true
          }
        };
      })
    };
  }
  onCellClick({ grid, column, record, target, event }) {
    if (target.classList.contains("b-rating-icon") && !grid.readOnly && column.editable) {
      let starIndex = [].indexOf.call(target.parentNode.childNodes, target);
      if (target.classList.contains("b-filled") && (event.metaKey || event.shiftKey)) {
        starIndex = starIndex - 1;
      }
      if (record.getValue(column.field) === 1 && starIndex === 0) {
        starIndex = -1;
      }
      record.set(column.field, starIndex + 1);
    }
  }
};
__publicField(RatingColumn, "$name", "RatingColumn");
__publicField(RatingColumn, "type", "rating");
// Type to use when auto adding field
__publicField(RatingColumn, "fieldType", "number");
__publicField(RatingColumn, "fields", [
  "emptyIcon",
  "filledIcon",
  "editable"
]);
ColumnStore.registerColumnType(RatingColumn, true);
RatingColumn.exposeProperties();
RatingColumn._$name = "RatingColumn";

// ../Grid/lib/Grid/column/RowNumberColumn.js
var RowNumberColumn = class extends Column {
  static get defaults() {
    return {
      /**
       * @hideconfigs groupable, sortable, filterable, searchable, resizable, draggable, cellEditor,
       * editTargetSelector, editor, field, finalizeCellEdit, instantUpdate, invalidAction, mergeCells,
       * mergeable, revertOnEscape
       */
      groupable: false,
      sortable: false,
      filterable: false,
      searchable: false,
      resizable: false,
      draggable: false,
      mergeable: false,
      minWidth: 50,
      width: 50,
      align: "right",
      text: "#",
      ariaLabel: "L{RowNumberColumn.rowNumber}",
      editor: false,
      readOnly: true
    };
  }
  construct(config) {
    super.construct(...arguments);
    const me = this, { grid } = me;
    me.internalCellCls = "b-row-number-cell";
    me.externalHeaderRenderer = me.headerRenderer;
    me.setData("headerRenderer", me.internalHeaderRenderer);
    if (grid) {
      grid.ion({
        bindStore: "bindStore",
        thisObj: me
      });
      me.bindStore({ store: grid.store, initial: true });
      if (grid.store.count && !grid.rendered) {
        grid.ion({
          paint: "resizeToFitContent",
          thisObj: me,
          once: true
        });
      }
    }
  }
  get groupHeaderReserved() {
    return true;
  }
  bindStore({ store, initial }) {
    const me = this;
    me.detachListeners("grid");
    store.ion({
      name: "grid",
      [`change${me.grid.asyncEventSuffix}`]: "onStoreChange",
      thisObj: me
    });
    if (!initial && !me.resizeToFitContent()) {
      me.measureOnRender();
    }
  }
  onStoreChange({ action, isMove }) {
    if (action === "dataset" || action === "add" || action === "remove" || action === "removeall") {
      if (action === "remove" && isMove) {
        return;
      }
      const result = this.resizeToFitContent();
      if (action === "dataset" && !result && this.grid.store.count) {
        this.measureOnRender();
      }
    }
  }
  measureOnRender() {
    this.grid.rowManager.ion({
      renderDone() {
        this.resizeToFitContent();
      },
      once: true,
      thisObj: this
    });
  }
  /**
   * Renderer that displays the row number in the cell.
   * @private
   */
  defaultRenderer({ record, grid }) {
    var _a5, _b2, _c2;
    let count = record.isSpecialRow ? "" : grid.store.indexOf(record, true) + 1;
    if ((_a5 = grid.splitFrom) == null ? void 0 : _a5.isLockedRows) {
      count += (_c2 = (_b2 = grid.splitFrom.features.lockRows) == null ? void 0 : _b2.numberOfLockedRows) != null ? _c2 : 0;
    }
    return count;
  }
  /**
   * Resizes the column to match the widest string in it.
   * @private
   */
  resizeToFitContent() {
    var _a5;
    const me = this, subViews = (_a5 = me.grid.features.lockRows) == null ? void 0 : _a5.subViews, grid = (subViews == null ? void 0 : subViews[1]) === me.grid ? subViews[0] : me.grid, { element } = grid, { store } = grid, { count } = store;
    if (count && !me.hidden) {
      const cellElement = element.querySelector(`.b-grid-cell[data-column-id="${me.id}"]`);
      if (cellElement) {
        const cellPadding = cellElement.isConnected ? parseInt(DomHelper.getStyleValue(cellElement, "padding-left")) : me._cachedCellPadding || 0, maxWidth = DomHelper.measureText(count, cellElement);
        me.width = Math.max(me.minWidth, maxWidth + 2 * cellPadding);
        me._cachedCellPadding = cellPadding;
        return true;
      }
    }
    return false;
  }
  set flex(f) {
  }
  internalHeaderRenderer({ headerElement, column }) {
    var _a5;
    headerElement.classList.add("b-rownumber-header");
    return ((_a5 = column.externalHeaderRenderer) == null ? void 0 : _a5.call(this, ...arguments)) || column.headerText;
  }
};
__publicField(RowNumberColumn, "$name", "RowNumberColumn");
__publicField(RowNumberColumn, "type", "rownumber");
ColumnStore.registerColumnType(RowNumberColumn, true);
RowNumberColumn._$name = "RowNumberColumn";

// ../Grid/lib/Grid/column/TemplateColumn.js
var TemplateColumn = class extends Column {
  static get defaults() {
    return {
      htmlEncode: false
    };
  }
  constructor(config, store) {
    super(...arguments);
    const me = this;
    if (!me.template) {
      throw new Error("TemplateColumn needs a template");
    }
    if (typeof me.template !== "function") {
      throw new Error("TemplateColumn.template must be a function");
    }
  }
  /**
   * Renderer that uses a template for cell content.
   * @private
   */
  defaultRenderer(renderData) {
    if (!renderData.record.isSpecialRow) {
      return this.template({
        value: renderData.value,
        record: renderData.record,
        field: this.field
      });
    }
  }
};
__publicField(TemplateColumn, "type", "template");
__publicField(TemplateColumn, "fields", [
  /**
   * Template function used to generate a value displayed in the cell. Called with arguments `{ value, record, field }`
   *
   * @config {Function} template
   * @param {Object} data An object that contains data about the cell being rendered.
   * @param {*} data.value The value (only present when you set a `field` on the column)
   * @param {Core.data.Model} data.record The record representing the row
   * @param {String} data.field The column field name
   * @returns {DomConfig|String|null} String or DomConfig object representing the HTML markup
   * @category Common
   */
  "template"
]);
ColumnStore.registerColumnType(TemplateColumn, true);
TemplateColumn.exposeProperties();
TemplateColumn._$name = "TemplateColumn";

// ../Grid/lib/Grid/column/TimeColumn.js
var TimeColumn = class extends Column {
  static get defaults() {
    return {
      /**
       * Time format
       * @config {String}
       * @category Common
       * @default
       */
      format: "LT",
      minWidth: 140,
      filterType: "time"
    };
  }
  //endregion
  //region Display
  /**
   * Renderer that displays the time with the specified format. Also adds cls 'b-time-cell' to the cell.
   * @private
   */
  defaultRenderer({ value: value2 }) {
    return value2 ? this.formatValue(value2) : "";
  }
  /**
   * Group renderer that displays the time with the specified format.
   * @private
   */
  groupRenderer({ cellElement, groupRowFor }) {
    cellElement.innerHTML = this.formatValue(groupRowFor);
  }
  //endregion
  //region Formatter
  /**
   * Used by both renderer and groupRenderer to do the actual formatting of the time
   * @private
   * @param value
   * @returns {String}
   */
  formatValue(value2) {
    if (typeof value2 === "string") {
      value2 = DateHelper.parse(value2, this.format);
    }
    return DateHelper.format(value2, this.format);
  }
  //endregion
  //region Getters/Setters
  /**
   * Get/Set format for time displayed in cell and editor (see {@link Core.helper.DateHelper#function-format-static} for formatting options)
   * @property {String}
   */
  set format(value2) {
    const { editor } = this;
    this.set("format", value2);
    if (editor) {
      editor.format = value2;
    }
  }
  get format() {
    return this.get("format");
  }
  get defaultEditor() {
    return {
      name: this.field,
      type: "timefield",
      format: this.format
    };
  }
  //endregion
};
__publicField(TimeColumn, "$name", "TimeColumn");
//region Config
__publicField(TimeColumn, "type", "time");
// Type to use when auto adding field
__publicField(TimeColumn, "fieldType", "date");
__publicField(TimeColumn, "fields", ["format"]);
ColumnStore.registerColumnType(TimeColumn, true);
TimeColumn.exposeProperties();
TimeColumn._$name = "TimeColumn";

// ../Grid/lib/Grid/column/TreeColumn.js
var TreeColumn = class extends Column {
  static get defaults() {
    return {
      tree: true,
      hideable: false,
      minWidth: 150
    };
  }
  static get fields() {
    return [
      /**
       * The icon to use for the collapse icon in collapsed state
       * @config {String|null} expandIconCls
       */
      { name: "expandIconCls", defaultValue: "b-icon b-icon-tree-expand" },
      /**
       * The icon to use for the collapse icon in expanded state
       * @config {String|null} collapseIconCls
       */
      { name: "collapseIconCls", defaultValue: "b-icon b-icon-tree-collapse" },
      /**
       * The icon to use for the collapse icon in expanded state
       * @config {String|null} collapsedFolderIconCls
       */
      { name: "collapsedFolderIconCls" },
      /**
       * The icon to use for the collapse icon in expanded state
       * @config {String|null} expandedFolderIconCls
       */
      { name: "expandedFolderIconCls" },
      /**
       * Size of the child indent in em. Resulting indent is indentSize multiplied by child level.
       * @config {Number} indentSize
       * @default 1.7
       */
      { name: "indentSize", defaultValue: 1.7 },
      /**
       * The icon to use for the leaf nodes in the tree
       * @config {String|null} leafIconCls
       */
      { name: "leafIconCls", defaultValue: "b-icon b-icon-tree-leaf" },
      { name: "editTargetSelector", defaultValue: ".b-tree-cell-value" },
      /**
       * Renderer function, used to format and style the content displayed in the cell. Return the cell text you
       * want to display. Can also affect other aspects of the cell, such as styling.
       *
       * <div class="note">
       * As the TreeColumn adds its own cell content to the column, there is a limit to what is supported in the
       * renderer function in comparison with an ordinary
       * {@link Grid.column.Column#config-renderer Column renderer}. Most notably is that changing `cellElement`
       * content can yield unexpected results as it will be updated later in the rendering process.
       * </div>
       *
       * You can also return a {@link Core.helper.DomHelper#typedef-DomConfig} object describing the markup
       * ```javascript
       * new Grid({
       *     columns : [
       *         {
       *              type  : 'tree',
       *              field : 'name'
       *              text  : 'Name',
       *              renderer : ({ record }) => {
       *                  return {
       *                      class : 'myClass',
       *                      children : [
       *                          {
       *                              tag : 'i',
       *                              class : 'fa fa-pen'
       *                          },
       *                          {
       *                              tag : 'span',
       *                              text : record.name
       *                          }
       *                      ]
       *                  };
       *              }
       *         }
       *     ]
       * });
       * ```
       *
       * You can modify the row element too from inside a renderer to add custom CSS classes:
       *
       * ```javascript
       * new Grid({
       *     columns : [
       *         {
       *             type     : 'tree',
       *             field    : 'name',
       *             text     : 'Name',
       *             renderer : ({ record, row }) => {
       *                // Add special CSS class to new rows that have not yet been saved
       *               row.cls.newRow = record.isPhantom;
       *
       *               return record.name;
       *         }
       *     ]
       * });
       * ```
       *
       * <div class="note">
       * You should never modify any records inside this method.
       * </div>
       *
       * @config {Function} renderer
       * @param {Object} renderData Object containing renderer parameters
       * @param {HTMLElement} renderData.cellElement Cell element, for adding CSS classes, styling etc. Can be `null` in case of export
       * @param {*} renderData.value Value to be displayed in the cell
       * @param {Core.data.Model} renderData.record Record for the row
       * @param {Grid.column.Column} renderData.column This column
       * @param {Grid.view.GridBase} renderData.grid This grid
       * @param {Grid.row.Row} renderData.row Row object. Can be null in case of export. Use the
       *   {@link Grid.row.Row#function-assignCls row's API} to manipulate CSS class names.
       * @param {Object} renderData.size Set `size.height` to specify the desired row height for the current row.
       *   Largest specified height is used, falling back to configured {@link Grid/view/Grid#config-rowHeight}
       *   in case none is specified. Can be null in case of export
       * @param {Number} renderData.size.height Set this to request a certain row height
       * @param {Number} renderData.size.configuredHeight Row height that will be used if none is requested
       * @param {Boolean} renderData.isExport `true` if record is being exported to Excel/textual format to allow special
       * handling during export.
       * @param {Boolean} renderData.isMeasuring True if the column is being measured for a `resizeToFitContent` call.
       *   In which case an advanced renderer might need to take different actions.
       * @returns {String|DomConfig|null}
       *
       * @category Rendering
       */
      "renderer"
    ];
  }
  construct(config, store, ...args) {
    super.construct(config, store, ...args);
    const me = this;
    me.shouldHtmlEncode = me.htmlEncode;
    me.setData("htmlEncode", false);
    const { renderer } = me;
    if (renderer && !(me.grid.splitFrom || me.grid.initialConfig.splitFrom)) {
      me.originalRenderer = renderer;
    }
    me.setData("renderer", me.treeRenderer.bind(me));
  }
  /**
   * A column renderer that is automatically added to the column with { tree: true }. It adds padding and node icons
   * to the cell to make the grid appear to be a tree. The original renderer is called in the process.
   * @private
   */
  treeRenderer(renderData) {
    var _a5, _b2, _c2, _d, _e, _f, _g;
    const me = this, {
      grid,
      cellElement,
      row,
      record,
      isExport
    } = renderData, { features } = grid;
    if (!features.tree) {
      throw new Error("TreeColumn requires the Tree feature to be enabled");
    }
    const gridMeta = record.instanceMeta(grid.store), isCollapsed = !record.isLeaf && gridMeta.collapsed, innerConfig = {
      className: "b-tree-cell-value"
    }, children = [innerConfig], result = {
      className: {
        "b-tree-cell-inner": 1
      },
      tag: record.href ? "a" : "div",
      href: record.href,
      target: record.target,
      children
    }, hasTreeLines = features.tree.treeLines, parentRenderer = grid.isTreeGrouped && !record.isLeaf && features.treeGroup.parentRenderer;
    let outputIsObject, iconCls, { value: value2 } = renderData, renderingColumn = me;
    if (me.originalRenderer || parentRenderer) {
      let rendererHtml;
      if (parentRenderer) {
        if (record.field) {
          renderingColumn = grid.columns.get(record.field);
          if (renderingColumn) {
            value2 = renderingColumn.isWidgetColumn ? value2 : (_b2 = (_a5 = renderingColumn === me ? renderingColumn.originalRenderer : renderingColumn.renderer) == null ? void 0 : _a5.call(renderingColumn, {
              ...renderData,
              column: renderingColumn,
              value: record.name,
              isTreeGroup: true
            })) != null ? _b2 : record.name;
          } else {
            value2 = record.name;
          }
        }
        rendererHtml = features.treeGroup.parentRenderer({
          ...renderData,
          field: record.field,
          value: value2,
          column: renderingColumn,
          record: record.firstGroupChild,
          grid
        });
      } else {
        rendererHtml = me.originalRenderer(renderData);
      }
      const hasFrameworkRenderer = (_c2 = grid.hasFrameworkRenderer) == null ? void 0 : _c2.call(grid, {
        cellContent: rendererHtml,
        renderingColumn
      });
      outputIsObject = typeof rendererHtml === "object" && !hasFrameworkRenderer;
      value2 = hasFrameworkRenderer ? "" : rendererHtml === false ? cellElement.innerHTML : rendererHtml;
      renderData.rendererHtml = rendererHtml;
    }
    if (!outputIsObject) {
      value2 = String(value2 != null ? value2 : "");
    }
    if (isExport) {
      return value2;
    }
    if (!record.isLeaf) {
      const isExpanded = record.isExpanded(grid.store), expanderIconCls = isExpanded ? me.collapseIconCls : me.expandIconCls, folderIconCls = isCollapsed ? me.expandedFolderIconCls : me.collapsedFolderIconCls;
      cellElement.classList.add("b-tree-parent-cell");
      cellElement.setAttribute("aria-expanded", isExpanded);
      row.toggleCls("b-loading-children", Boolean(gridMeta.isLoadingChildren));
      children.unshift({
        tag: "i",
        className: {
          "b-tree-expander": 1,
          [expanderIconCls]: 1,
          "b-empty-parent": !gridMeta.isLoadingChildren && (record.children !== true && !((_d = record.children) == null ? void 0 : _d.length))
        }
      });
      record.meta.hasIcon = iconCls = renderData.iconCls || record.iconCls || folderIconCls;
    } else {
      cellElement.classList.add("b-tree-leaf-cell");
      iconCls = renderData.iconCls || record.iconCls || me.leafIconCls;
    }
    if (iconCls) {
      children.splice(children.length - 1, 0, {
        tag: "i",
        "aria-hidden": "true",
        className: {
          "b-tree-icon": 1,
          [iconCls]: 1
        }
      });
    }
    if (row.isRow && !record.isLeaf) {
      row.setAttribute("aria-expanded", !isCollapsed);
      if (isCollapsed) {
        row.removeAttribute("aria-owns");
      } else {
        for (const region in grid.subGrids) {
          const el = row.elements[region];
          DomHelper.setAttributes(el, {
            "aria-owns": ((_e = record.children) == null ? void 0 : _e.length) ? (_f = record.children) == null ? void 0 : _f.slice(0, 50).map((r) => `${grid.id}-${region}-${r.id}`).join(" ") : null
          });
        }
      }
    }
    if (Array.isArray(value2)) {
      innerConfig.children = value2;
    } else if (outputIsObject) {
      Object.assign(innerConfig, value2);
    } else if ((renderingColumn == null ? void 0 : renderingColumn.shouldHtmlEncode) || !value2.includes("<")) {
      result.className["b-text-value"] = 1;
      innerConfig.text = value2;
    } else {
      innerConfig.html = value2;
    }
    let padding = 0;
    if (record.isLeaf) {
      padding += ((_g = record.parent) == null ? void 0 : _g.meta.hasIcon) ? 2.2 : iconCls ? 0.5 : 0.4;
    } else {
      padding += iconCls ? -0.15 : 0;
    }
    const treeLineBlocks = [];
    if (hasTreeLines) {
      for (let i = 0; i < record.childLevel; i++) {
        treeLineBlocks.push({
          tag: "span",
          // take half the indentSize as a margin on each side of the line, move by 0.2em to center under the chevron icon and subtract the border width of 2px
          style: `margin: 0 ${me.indentSize / 2 - 0.2 - 0.125}em 0 ${me.indentSize / 2 + 0.2}em`,
          className: `b-tree-line b-tree-line-${i}`
        });
      }
    } else {
      padding += record.childLevel * me.indentSize;
    }
    result.style = `padding-inline-start:${padding}em`;
    return hasTreeLines ? [...treeLineBlocks, result] : result;
  }
  // This function is not meant to be called by any code other than Base#getCurrentConfig().
  // It extracts the current configs (fields) for the column, with special handling for the renderer
  getCurrentConfig(options) {
    const result = super.getCurrentConfig(options);
    result.renderer = this.originalRenderer;
    return result;
  }
  isTreeExpanderIcon(element) {
    return element.matches("." + this.expandIconCls.trim().replace(/\s/g, ".") + ",." + this.collapseIconCls.trim().replace(/\s/g, "."));
  }
};
__publicField(TreeColumn, "$name", "TreeColumn");
__publicField(TreeColumn, "type", "tree");
ColumnStore.registerColumnType(TreeColumn, true);
TreeColumn.exposeProperties();
TreeColumn._$name = "TreeColumn";

// ../Grid/lib/Grid/data/GridRowModel.js
var GridRowModel = class extends Model {
  static get fields() {
    return [
      /**
       * Icon for row (used automatically in tree, feel free to use it in renderer in other cases)
       * @field {String} iconCls
       * @category Styling
       */
      {
        name: "iconCls",
        internal: true
      },
      /**
       * CSS class (or several classes divided by space) to append to row elements
       * @field {String} cls
       * @category Styling
       */
      {
        name: "cls",
        internal: true
      },
      /**
       * Used by the default implementation of {@link Grid.view.GridBase#config-getRowHeight} to determine row
       * height. Set it to use another height than the default for the records row.
       * @field {Number} rowHeight
       * @category Styling
       */
      {
        name: "rowHeight",
        internal: true
      },
      /**
       * A link to use for this record when rendered into a {@link Grid.column.TreeColumn}.
       * @field {String} href
       * @category Tree
       */
      {
        name: "href",
        internal: true
      },
      /**
       * The target to use if this tree node provides a value for the {@link #field-href} field.
       * @field {'_self'|'_blank'|'_parent'|'_top'|null} target
       * @category Tree
       */
      {
        name: "target",
        internal: true
      },
      /**
       * A boolean field used by the {@link Grid.feature.LockRows} feature for pinning a row to the top.
       * @field {Boolean} fixed
       * @category Misc
       */
      {
        name: "fixed",
        type: "boolean",
        internal: true
      }
    ];
  }
};
GridRowModel.exposeProperties();
GridRowModel._$name = "GridRowModel";

// ../Grid/lib/Grid/feature/base/CopyPasteBase.js
var actions = {
  cut: 1,
  copy: 1,
  paste: 1
};
var CopyPasteBase = class extends InstancePlugin.mixin(Clipboardable_default) {
  // Internal backwards compatibility
  get clipboardRecords() {
    return this.clipboardData || [];
  }
  isActionAvailable({ event, actionName }) {
    var _a5, _b2;
    if (actions[actionName]) {
      return !this.disabled && globalThis.getSelection().toString().length === 0 && (actionName === "copy" || !this.copyOnly) && // Do not allow cut or paste if copyOnly flag is set
      !((_a5 = this.client.features.cellEdit) == null ? void 0 : _a5.isEditing) && !((_b2 = this.client.focusedCell) == null ? void 0 : _b2.isSpecialRow) && !DomHelper.isEditable(event.target, true);
    }
    return false;
  }
  /**
   * Used by CellCopyPaste and RowCopyPaste to generate string representations of grid records
   * @param cells
   * @returns {String}
   * @private
   */
  cellsToString(cells) {
    var _a5;
    const me = this;
    let lastRowIndex = 0, lastColIndex = 0, stringData = "";
    cells.sort((c1, c2) => c1.rowIndex === c2.rowIndex ? c1.columnIndex - c2.columnIndex : c1.rowIndex - c2.rowIndex);
    for (const cell of cells) {
      const { record, _column: column, rowIndex, columnIndex } = cell;
      if (rowIndex > lastRowIndex) {
        if (stringData.length > 0) {
          stringData += "\n".repeat(rowIndex - lastRowIndex);
        }
        lastRowIndex = rowIndex;
        lastColIndex = columnIndex;
      } else if (columnIndex > lastColIndex) {
        if (stringData.length > 0) {
          stringData += "	".repeat(columnIndex - lastColIndex);
        }
        lastColIndex = columnIndex;
      }
      let cellValue = (_a5 = column == null ? void 0 : column.toClipboardString) == null ? void 0 : _a5.call(column, cell);
      if (cellValue === void 0) {
        cellValue = record.getValue(column.field);
        if (cellValue instanceof Date) {
          cellValue = DateHelper.format(cellValue, me.dateFormat);
        } else {
          cellValue = cellValue == null ? void 0 : cellValue.toString();
        }
      }
      if (me.toCopyString) {
        cellValue = me.toCopyString({ currentValue: cellValue, column, record });
      }
      cellValue = cellValue == null ? void 0 : cellValue.replaceAll(/[\n\t]/g, " ");
      stringData += cellValue || me.emptyValueChar;
    }
    return stringData;
  }
  /**
   * Sets tab and new-line separated string data into records.
   * Used by CellCopyPaste to set values into existing records.
   * Used by RowCopyPaste to create new records from values
   * @param clipboardData
   * @param createNewRecords If `false`, a selected cell is required and data will be set to existing records
   * @param store The store which to set/create new data to. Defaults to the clients default store.
   * @param fields Provide an array of string fields to create records instead of using columns
   * @returns {Object} modificationData
   * @private
   */
  setFromStringData(clipboardData, createNewRecords = false, store = this.client.store, fields) {
    var _a5;
    const me = this, { client } = me, {
      columns,
      selectedCells,
      _shiftSelectRange
    } = client, modifiedRecords = /* @__PURE__ */ new Set(), rows = me.stringAs2dArray(clipboardData), targetCells = [], affectedCells = [];
    if (!createNewRecords && (_shiftSelectRange == null ? void 0 : _shiftSelectRange.length) === selectedCells.length) {
      const cellRows = me.cellSelectorsAs2dArray(_shiftSelectRange);
      if ((cellRows == null ? void 0 : cellRows.length) % rows.length === 0 && cellRows.columnCount % rows.columnCount === 0) {
        for (let curI = 0; curI < cellRows.length; curI += rows.length) {
          for (let curX = 0; curX < cellRows.columnCount; curX += rows.columnCount) {
            targetCells.push(cellRows[curI][curX]);
          }
        }
      }
    } else if (!createNewRecords && selectedCells.length > 1 && rows.length === 1 && rows[0].length === 1) {
      targetCells.push(...selectedCells);
    }
    if (!targetCells.length) {
      targetCells.push(selectedCells == null ? void 0 : selectedCells[0]);
    }
    for (const targetCell of targetCells) {
      for (let rI = 0; rI < rows.length; rI++) {
        const row = rows[rI], targetRecord = createNewRecords ? store.createRecord() : store.getAt(targetCell.rowIndex + rI);
        if (targetRecord && !targetRecord.readOnly) {
          for (let cI = 0; cI < row.length; cI++) {
            const targetColumn = fields ? null : columns.visibleColumns[createNewRecords ? cI : targetCell.columnIndex + cI], targetField = (targetColumn == null ? void 0 : targetColumn.field) || (fields == null ? void 0 : fields[cI]);
            let value2 = row[cI];
            if (targetField && value2 && !(targetColumn == null ? void 0 : targetColumn.readOnly)) {
              if (value2 === me.emptyValueChar) {
                value2 = null;
              }
              if (targetColumn == null ? void 0 : targetColumn.fromClipboardString) {
                value2 = targetColumn.fromClipboardString({
                  string: value2,
                  record: targetRecord
                });
              }
              if (me.toPasteValue) {
                value2 = me.toPasteValue({
                  currentValue: value2,
                  record: targetRecord,
                  column: targetColumn,
                  field: targetField
                });
              }
              if (typeof value2 === "string" && ((_a5 = targetRecord.getFieldDefinition(targetField)) == null ? void 0 : _a5.isDateDataField)) {
                const parsedDate = DateHelper.parse(value2, me.dateFormat);
                if (!isNaN(parsedDate.getTime())) {
                  value2 = parsedDate;
                }
              }
              targetRecord.set(targetField, value2, false, false, false, true);
              if (targetColumn) {
                affectedCells.push(client.normalizeCellContext({ column: targetColumn, record: targetRecord }));
              }
            }
          }
          modifiedRecords.add(targetRecord);
        }
      }
    }
    return {
      modifiedRecords: [...modifiedRecords],
      targetCells: affectedCells
    };
  }
  /**
   * Converts an array of GridLocation objects to a two-dimensional array where first level is rows and second level is
   * columns. If the array is inconsistent in the number of columns present for each row, the function will return
   * false.
   * @param {Grid.util.GridLocation[]} locations
   * @private
   */
  cellSelectorsAs2dArray(locations) {
    const rows = [];
    let rId = null, columns;
    for (const location2 of locations) {
      if (location2.id !== rId) {
        rId = location2.id;
        columns = [];
        rows.push(columns);
      }
      columns.push(location2);
    }
    rows.columnCount = rows[0].length;
    if (rows.some((row) => row.length !== rows.columnCount)) {
      return false;
    }
    return rows;
  }
  /**
   * Converts a new-line- and tab-separated string to a two-dimensional array where first level is rows and second
   * level is columns. If the string is inconsistent in the number of columns present for each row, the function will
   * return false.
   * @param {String} string String values separated with new-line(\n,\r or similar) and tabs (\t)
   * @private
   */
  stringAs2dArray(string2) {
    const rows = [], stringRows = string2.split(/\r\n|(?!\r\n)[\n-\r\x85\u2028\u2029]/).filter((s) => s.length);
    for (const row of stringRows) {
      const columns = row.split("	");
      if (rows.columnCount && columns.length !== rows.columnCount) {
        return false;
      }
      rows.columnCount = columns.length;
      rows.push(columns);
    }
    return rows;
  }
};
__publicField(CopyPasteBase, "configurable", {
  /**
   * If `true` this prevents cutting and pasting. Will default to `true` if {@link Grid/feature/CellEdit} feature
   * is disabled. Set to `false` to prevent this behaviour.
   * @config {Boolean}
   */
  copyOnly: null,
  /**
   * Default keyMap configuration: Ctrl/Cmd+c to copy, Ctrl/Cmd+x to cut and Ctrl/Cmd+v to paste. These keyboard
   * shortcuts require a selection to be made.
   * @config {Object<String,KeyMapConfig>}
   */
  keyMap: {
    "Ctrl+C": "copy",
    "Ctrl+X": "cut",
    "Ctrl+V": "paste"
  },
  /**
   * Set this to `false` to not use native Clipboard API even if it is available
   * @config {Boolean}
   * @default
   */
  useNativeClipboard: false,
  /**
   * Provide a function to be able to customize the string value which is copied
   *
   * ```javascript
   * new Grid({
   *     features : {
   *         cellCopyPaste : {
   *             toCopyString({currentValue, column, record}) {
   *                 if(record.isAvatar){
   *                     return record.fullName;
   *                 }
   *                 return currentValue;
   *             }
   *         }
   *     }
   * });
   * ```
   *
   * Note that this function is only called when copying cell values or copying values from other Bryntum
   * component instances or from native clipboard.
   *
   * @config {Function}
   * @param {Object} data
   * @param {String} data.currentValue
   * @param {Grid.column.Column} data.column
   * @param {Core.data.Model} data.record
   * @returns {String}
   */
  toCopyString: null,
  /**
   * Provide a function to be able to customize the value which will be set onto the record
   *
   * ```javascript
   * new Grid({
   *     features : {
   *         cellCopyPaste : {
   *             toPasteValue({currentValue, column, record, field}) {
   *                 if(typeof currentValue === 'string'){
   *                     return currentValue.replace('$', '');
   *                 }
   *                 return currentValue;
   *             }
   *         }
   *     }
   * });
   * ```
   *
   * Note that this function is only called when pasting string values, either from CellCopyPaste or copying
   * values from other Bryntum component instances or from native clipboard.
   *
   * @config {Function}
   * @param {Object} data
   * @param {String} data.currentValue
   * @param {Grid.column.Column} data.column
   * @param {Core.data.Model} data.record
   * @returns {String}
   */
  toPasteValue: null,
  /**
   * If an empty value (null or empty string) is copied or cut, this config will replace that value.
   * This allows for clipboard data to skip columns.
   *
   * For example, look at these two selections
   * |  ROW  |   0  |      1       |       2      |   3  |
   * |-------|------|--------------|--------------|------|
   * | ROW 1 | SEL1 | not selected | not selected | SEL2 |
   * | ROW 2 | SEL3 | SEL4 (empty) | SEL5 (empty) | SEL6 |
   *
   * The clipboardData for `ROW 1` will look like this:
   `* SEL1\t\t\SEl2\nSEL3\t\t\SEL4`
   *
   * And `ROW 2` will look like this:
   * `SEL3\t\u{0020}\t\u{0020}\tSEL6`
   *
   * `ROW 1` will set value `SEL1` at column index 0 and `SEL2` at column index 3. This leaves column index 1 and
   * 2 untouched.
   *
   * `ROW 2` will set value `SEL3` at column index 0, `u{0020}` at column index 1 and 2, and `SEL`6 at column
   * index 3.
   *
   * The default `u{0020}` is a blank space.
   *
   * Note that this only applies when copy-pasting cell values or copying rows from other Bryntum component
   * instances or from native clipboard.
   *
   * @config {String}
   * @default
   */
  emptyValueChar: " ",
  /**
   * The format a copied date value should have when converted to a string. To learn more about available formats,
   * check out {@link Core.helper.DateHelper} docs.
   * @config {String}
   */
  dateFormat: "lll",
  /**
   * A method used to generate the name for a copy-pasted record. By defaults appends "- 2", "- 3" as a suffix.
   * Override it to provide your own naming of pasted records.
   *
   * @prp {Function}
   * @param {Core.data.Model} record The new record being pasted
   * @param {Core.data.Model} originalRecord The record that was copied
   * @returns {String}
   */
  generateNewName(record, originalRecord, store = this.client.store) {
    const { nameField } = this;
    let originalName = record.getValue(nameField), counter = 2;
    if ((originalRecord == null ? void 0 : originalRecord.meta.generatedName) === originalName) {
      originalName = originalRecord.meta.originalName;
    }
    let newName = originalName;
    if (newName != null && newName !== "") {
      while (store.findRecord(nameField, newName)) {
        newName = `${originalName} - ${counter}`;
        counter += 1;
      }
    }
    record.meta.generatedName = newName;
    record.meta.originalName = originalName;
    return newName;
  }
});
CopyPasteBase._$name = "CopyPasteBase";

// ../Grid/lib/Grid/feature/GridFeatureManager.js
var consumerToFeatureMap = /* @__PURE__ */ new Map();
var consumerToDefaultFeatureMap = /* @__PURE__ */ new Map();
var DEFAULT_FOR_TYPE = "Grid";
var remapToBase = {
  Grid: "GridBase",
  Scheduler: "SchedulerBase",
  SchedulerPro: "SchedulerProBase",
  Gantt: "GanttBase"
};
var classNameFix = /\$\d+$/;
var GridFeatureManager = class {
  /**
   * Register a feature class with the Grid. Enables it to be created and configured using config Grid#features.
   * @param {Function} featureClass The feature class constructor to register
   * @param {Boolean} [onByDefault] Specify true to have the feature enabled per default
   * @param {String|String[]} [forType] Specify a type to let the class applying the feature to determine if it should
   * use it
   */
  static registerFeature(featureClass, onByDefault = false, forType = null, as = null) {
    as = StringHelper.uncapitalize(as || Object.prototype.hasOwnProperty.call(featureClass, "$name") && featureClass.$$name || featureClass.name);
    as = as.replace(classNameFix, "");
    if (!Array.isArray(forType)) {
      forType = [forType || DEFAULT_FOR_TYPE];
    }
    forType.forEach((forType2) => {
      const type = remapToBase[forType2] || forType2, consumerFeaturesMap = consumerToFeatureMap.get(type) || /* @__PURE__ */ new Map(), consumerDefaultFeaturesMap = consumerToDefaultFeatureMap.get(type) || /* @__PURE__ */ new Map();
      consumerFeaturesMap.set(as, featureClass);
      consumerDefaultFeaturesMap.set(featureClass, onByDefault);
      consumerToFeatureMap.set(type, consumerFeaturesMap);
      consumerToDefaultFeatureMap.set(type, consumerDefaultFeaturesMap);
    });
  }
  /**
   * Get all the features registered for the given type name in an object where keys are feature names and values are
   * feature constructors.
   *
   * @param {String} [forType]
   * @returns {Object}
   */
  static getTypeNameFeatures(forType = DEFAULT_FOR_TYPE) {
    const type = remapToBase[forType] || forType, consumerFeaturesMap = consumerToFeatureMap.get(type), features = {};
    if (consumerFeaturesMap) {
      consumerFeaturesMap.forEach((featureClass, as) => features[as] = featureClass);
    }
    return features;
  }
  /**
   * Get all the default features registered for the given type name in an object where keys are feature names and
   * values are feature constructors.
   *
   * @param {String} [forType]
   * @returns {Object}
   */
  static getTypeNameDefaultFeatures(forType = DEFAULT_FOR_TYPE) {
    const type = remapToBase[forType] || forType, consumerFeaturesMap = consumerToFeatureMap.get(type), consumerDefaultFeaturesMap = consumerToDefaultFeatureMap.get(type);
    const features = {};
    if (consumerFeaturesMap && consumerDefaultFeaturesMap) {
      consumerFeaturesMap.forEach((featureClass, as) => {
        if (consumerDefaultFeaturesMap.get(featureClass)) {
          features[as] = featureClass;
        }
      });
    }
    return features;
  }
  /**
   * Gets all the features registered for the given instance type name chain. First builds the type name chain then
   * queries for features for each type name and combines them into one object, see
   * {@link #function-getTypeNameFeatures-static}() for returned object description.
   *
   * If feature is registered for both parent and child type name then feature for child overrides feature for parent.
   *
   * @param {Object} instance
   * @returns {Object}
   */
  static getInstanceFeatures(instance) {
    return instance.$meta.names.reduce(
      (features, typeName) => Object.assign(features, this.getTypeNameFeatures(typeName)),
      {}
    );
  }
  /**
   * Gets all the *default* features registered for the given instance type name chain. First builds the type name
   * chain then queries for features for each type name and combines them into one object, see
   * {@link #function-getTypeNameFeatures-static}() for returned object description.
   *
   * If feature is registered for both parent and child type name then feature for child overrides feature for parent.
   *
   * @param {Object} instance
   * @returns {Object}
   */
  static getInstanceDefaultFeatures(instance) {
    return instance.$meta.names.reduce(
      (features, typeName) => Object.entries(
        this.getTypeNameFeatures(typeName)
      ).reduce(
        (features2, [as, featureClass]) => {
          if (this.isDefaultFeatureForTypeName(featureClass, typeName)) {
            features2[as] = featureClass;
          } else {
            delete features2[as];
          }
          return features2;
        },
        features
      ),
      {}
    );
  }
  /**
   * Checks if the given feature class is default for the type name
   *
   * @param {Core.mixin.InstancePlugin} featureClass Feature to check
   * @param {String} [forType]
   * @returns {Boolean}
   */
  static isDefaultFeatureForTypeName(featureClass, forType = DEFAULT_FOR_TYPE) {
    const type = remapToBase[forType] || forType, consumerDefaultFeaturesMap = consumerToDefaultFeatureMap.get(type);
    return consumerDefaultFeaturesMap && consumerDefaultFeaturesMap.get(featureClass) || false;
  }
  /**
   * Checks if the given feature class is default for the given instance type name chain. If the feature is not
   * default for the parent type name but it is for the child type name, then the child setting overrides the parent
   * one.
   *
   * @param {Core.mixin.InstancePlugin} featureClass Feature to check
   * @param {String} [forType]
   * @returns {Boolean}
   */
  static isDefaultFeatureForInstance(featureClass, instance) {
    const typeChain = instance.$meta.names.slice().reverse();
    let result = null;
    for (let i = 0, len = typeChain.length; i < len && result === null; ++i) {
      const consumerDefaultFeaturesMap = consumerToDefaultFeatureMap.get(typeChain[i]);
      if (consumerDefaultFeaturesMap && consumerDefaultFeaturesMap.has(featureClass)) {
        result = consumerDefaultFeaturesMap.get(featureClass);
      }
    }
    return result || false;
  }
  /**
   * Resets feature registration date, used in tests to reset state after test
   *
   * @internal
   */
  static reset() {
    consumerToFeatureMap.clear();
    consumerToDefaultFeatureMap.clear();
  }
};

// ../Grid/lib/Grid/feature/CellCopyPaste.js
var CellCopyPaste = class extends CopyPasteBase {
  constructor() {
    super(...arguments);
    // Used in events to separate events from different features from each other
    __publicField(this, "entityName", "cell");
  }
  static get pluginConfig() {
    return {
      chain: [
        "populateCellMenu",
        "afterSelectionModeChange"
      ]
    };
  }
  afterConstruct() {
    super.afterConstruct();
    this.afterSelectionModeChange();
  }
  afterSelectionModeChange() {
    const me = this;
    if (!me.client.selectionMode.cell) {
      me.disabled = true;
    } else if (me._disabledBySelectionMode) {
      me.disabled = false;
      delete me._disabledBySelectionMode;
    }
  }
  set copyOnly(value2) {
    this._copyOnly = value2;
  }
  get copyOnly() {
    var _a5;
    if (this._copyOnly == null) {
      return !((_a5 = this.client.features.cellEdit) == null ? void 0 : _a5.enabled);
    }
    return Boolean(this._copyOnly);
  }
  get canCopy() {
    var _a5;
    const { client } = this;
    return Boolean(!this.disabled && client.selectedCells.length && (!client._selectedRows.length || ((_a5 = client.features.rowCopyPaste) == null ? void 0 : _a5.disabled) || client.focusedCell && client.isCellSelected(client.focusedCell)));
  }
  get canCutPaste() {
    var _a5;
    return this.canCopy && !this.copyOnly && !((_a5 = this.client.features.cellEdit) == null ? void 0 : _a5.isEditing) && !this.client.readOnly;
  }
  // Called from keyMap. Also used internally here
  isActionAvailable({ actionName }) {
    return super.isActionAvailable(...arguments) && this.canCopy && (actionName === "copy" || this.canCutPaste);
  }
  /**
   * Cuts selected cells to clipboard (native if accessible) to paste later
   * @async
   */
  async cut() {
    await this.copy(true);
  }
  /**
   * Copies selected cells to clipboard (native if accessible) to paste later
   * @async
   */
  async copy(isCut = false) {
    if (typeof isCut != "boolean") {
      isCut = false;
    }
    const me = this, { selectedCells } = me.client, cells = isCut ? selectedCells.filter((r) => {
      var _a5;
      return !((_a5 = r.record) == null ? void 0 : _a5.readOnly);
    }) : selectedCells;
    if (cells) {
      if (isCut ? !me.canCutPaste : !me.canCopy) {
        return;
      }
      const copiedDataString = me.cellsToString(cells);
      if (await me.writeToClipboard(copiedDataString, isCut, { cells }) === void 0) {
        return;
      }
      if (isCut === true) {
        for (const cell of cells) {
          const { column, record } = cell;
          if (!column.readOnly) {
            let string2 = null;
            if (column.fromClipboardString) {
              string2 = column.fromClipboardString({ string: string2, record });
            }
            cell.record.set(cell.column.field, string2);
          }
        }
      }
      me.client.trigger("copy", { cells, copiedDataString, isCut, entityName: me.entityName });
    }
  }
  // Called from Clipboardable before writing to the clipboard
  async beforeCopy({ data, isCut, cells }) {
    return await this.client.trigger("beforeCopy", { cells, data, isCut, entityName: this.entityName });
  }
  /**
   * Pastes string data into a cell or a range of cells. Either from native clipboard if that is accessible or from a
   * fallback clipboard that is only available to the owner Grid.
   *
   * The string data will be split on `\n` and `\t` and put in different rows and columns accordingly.
   *
   * Note that there must be a selected cell to paste the data into.
   * @async
   */
  async paste() {
    var _a5;
    const me = this, { client, entityName } = me, targetCell = client.selectedCells[0];
    if (!me.canCutPaste || !targetCell) {
      return;
    }
    const clipboardData = (_a5 = await me.readFromClipboard({}, true)) == null ? void 0 : _a5.data;
    if (!clipboardData) {
      return;
    }
    const { modifiedRecords, targetCells } = me.setFromStringData(clipboardData);
    if (client.selectedCells.length === 1 && targetCells.length > 1) {
      client.selectCellRange(targetCells[0], targetCells[targetCells.length - 1]);
    }
    client.trigger("paste", { clipboardData, targetCell, modifiedRecords: [...modifiedRecords], entityName });
  }
  // Called from Clipboardable before finishing the clipboard read
  async beforePaste({ data, text }) {
    return await this.client.trigger("beforePaste", {
      clipboardData: data,
      targetCell: this.client.selectedCell,
      entityName: this.entityName,
      text
    });
  }
  populateCellMenu({ record, items: items2 }) {
    const me = this;
    if (me.canCopy) {
      items2.cutCell = {
        text: me.cutText,
        localeClass: me,
        icon: "b-icon b-icon-cut",
        weight: 115,
        disabled: record.readOnly || !me.canCutPaste,
        onItem: () => me.cut()
      };
      items2.pasteCell = {
        text: me.pasteText,
        localeClass: me,
        icon: "b-icon b-icon-paste",
        weight: 120,
        disabled: record.readOnly || !me.canCutPaste || me.hasClipboardData() === false,
        onItem: () => me.paste()
      };
      items2.copyCell = {
        text: me.copyText,
        localeClass: me,
        cls: "b-separator",
        icon: "b-icon b-icon-copy",
        weight: 110,
        onItem: () => me.copy()
      };
    }
  }
};
__publicField(CellCopyPaste, "$name", "CellCopyPaste");
__publicField(CellCopyPaste, "configurable", {
  /**
   * Set this to `false` to not use native Clipboard API even if it is available
   * @config {Boolean}
   * @default true
   */
  useNativeClipboard: !VersionHelper.isTestEnv,
  copyText: "L{copy}",
  cutText: "L{cut}",
  pasteText: "L{paste}",
  // Disables non-string data copying
  allowedDataSources: "none"
});
CellCopyPaste._$name = "CellCopyPaste";
GridFeatureManager.registerFeature(CellCopyPaste);

// ../Grid/lib/Grid/feature/base/GridEditBase.js
var GridEditBase = class extends Delayable_default(InstancePlugin) {
  static get pluginConfig() {
    return {
      assign: ["startEditing", "finishEditing", "cancelEditing"],
      before: ["onElementKeyDown", "onElementPointerUp"],
      chain: ["onElementClick", "bindStore"]
    };
  }
  construct(grid, config) {
    super.construct(grid, config);
    const me = this, gridListeners = {
      cellClick: "onCellClick",
      readOnly: "syncEditingReadOnly",
      thisObj: me
    };
    me.grid = grid;
    if (me.triggerEvent !== "cellclick") {
      gridListeners[me.triggerEvent] = "onTriggerEditEvent";
    }
    grid.ion(gridListeners);
    me.bindStore(grid.store);
  }
  doDestroy() {
    this.grid.columns.allRecords.forEach((column) => {
      var _a5;
      (_a5 = column._cellEditor) == null ? void 0 : _a5.destroy();
    });
    super.doDestroy();
  }
  doDisable(disable) {
    if (disable && !this.isConfiguring) {
      this.cancelEditing(true);
    }
    super.doDisable(disable);
  }
  set disabled(disabled) {
    super.disabled = disabled;
  }
  get disabled() {
    const { grid } = this;
    return Boolean(super.disabled || grid.disabled || grid.readOnly);
  }
  // region Editing
  /**
   * Is editing currently active?
   * @readonly
   * @property {Boolean}
   */
  get isEditing() {
    return Boolean(this.editorContext);
  }
  /**
   * Returns the record currently being edited, or `null`
   * @readonly
   * @property {Core.data.Model}
   */
  get activeRecord() {
    var _a5;
    return ((_a5 = this.editorContext) == null ? void 0 : _a5.record) || null;
  }
  /**
   * Event handler added when editing is active called when user clicks a cell in the grid during editing.
   * It finishes editing and moves editor to the selected cell instead.
   * @private
   * @category Internal event handling
   */
  async onCellClickWhileEditing({ event, cellSelector }) {
    const me = this;
    if (event.target.closest(".b-editor")) {
      return;
    }
    if (DomHelper.isTouchEvent || event.target.matches(me.ignoreCSSSelector)) {
      await me.finishEditing();
      return;
    }
    if (me.finishEditingPromise) {
      return;
    }
    if (me.editorContext && !me.editorContext.editor.owns(event.target)) {
      if (me.getEditingContext(cellSelector)) {
        if (me.isRowEdit) {
          if (me.continueEditingOnCellClick) {
            await me.startEditing(cellSelector);
          } else {
            me.cancelEditing();
          }
        } else if (await me.finishEditing()) {
          if (me.continueEditingOnCellClick) {
            await me.startEditing(cellSelector);
          }
        } else {
          me.grid.focusCell(me.editorContext);
          me.editor.focus();
        }
      } else {
        await me.finishEditing();
      }
    }
  }
  /**
   * Starts editing if user taps selected cell again on touch device. Chained function called when user clicks a cell.
   * @private
   * @category Internal event handling
   */
  async onCellClick({ cellSelector, target, event, column }) {
    const me = this, { focusedCell } = me.client;
    if (target.closest(".b-tree-expander")) {
      return false;
    } else if (DomHelper.isTouchEvent && me._lastCellClicked === (focusedCell == null ? void 0 : focusedCell.cell) && event.timeStamp - me.touchEditDelay > me._lastCellClickedTime) {
      await me.startEditing(cellSelector);
    } else if (this.triggerEvent === "cellclick") {
      await me.onTriggerEditEvent(...arguments);
    }
    me._lastCellClicked = focusedCell == null ? void 0 : focusedCell.cell;
    me._lastCellClickedTime = event.timeStamp;
  }
  // onElementPointerUp should be used to cancel editing before toggleCollapse handled
  // otherwise data collisions may be happened
  onElementPointerUp(event) {
    if (event.target.closest(".b-tree-expander")) {
      this.cancelEditing(void 0, event);
    }
  }
  /**
   * Called when the user triggers the edit action in {@link #config-triggerEvent} config. Starts editing.
   * @private
   * @category Internal event handling
   */
  async onTriggerEditEvent({ cellSelector, target, event, column }) {
    var _a5;
    const { editorContext, client } = this;
    if (column.preventCellEditViaMouse || target.closest(".b-tree-expander") || DomHelper.isTouchEvent && event.type === "dblclick") {
      return;
    }
    if (event && ((_a5 = client.features.cellMenu) == null ? void 0 : _a5.triggerEvent) === event.type) {
      return;
    }
    if (editorContext) {
      if (editorContext.equals(this.grid.normalizeCellContext(cellSelector)) || !await this.finishEditing()) {
        return;
      }
    }
    await this.startEditing(cellSelector);
  }
  /**
   * Update the input field if underlying data changes during edit.
   * @private
   * @category Internal event handling
   */
  onStoreUpdate({ changes, record }) {
    const { editorContext } = this;
    if (editorContext == null ? void 0 : editorContext.editor.isVisible) {
      if (record === editorContext.record && (changes.readOnly || !editorContext.editor.dataField || editorContext.editor.dataField in changes)) {
        editorContext.editor.refreshEdit();
      }
    }
  }
  onStoreRemove({ changes, records }) {
    const { editorContext } = this;
    if ((editorContext == null ? void 0 : editorContext.editor.isVisible) && records.includes(editorContext.editor.record)) {
      this.cancelEditing();
    }
  }
  // endregion
};
__publicField(GridEditBase, "name", "GridEditBase");
__publicField(GridEditBase, "configurable", {
  /**
   * By default, while edtting, clicking on a grid cell outside of the editor will commence editing for the clicked cell context.
   *
   * Set to `false` to stop editing when clicking another cell while editing.
   * @config {Boolean}
   * @default
   */
  continueEditingOnCellClick: true,
  /**
   * Set to `true` to start editing when user starts typing text on a focused cell (as in Excel)
   * @config {Boolean}
   * @default false
   */
  autoEdit: null,
  /**
   * The name of the grid event that will trigger cell editing. Defaults to
   * {@link Grid.view.mixin.GridElementEvents#event-cellDblClick celldblclick} but can be changed to any other event,
   * such as {@link Grid.view.mixin.GridElementEvents#event-cellClick cellclick}.
   *
   * ```javascript
   * features : {
   *     cellEdit : {
   *         triggerEvent : 'cellclick'
   *     }
   * }
   * ```
   *
   * @config {String}
   * @default
   */
  triggerEvent: "celldblclick",
  /**
   * A CSS selector for elements that when clicked, should not trigger editing. Useful if you render actionable
   * icons or buttons into a grid cell.
   * @config {String}
   * @default
   */
  ignoreCSSSelector: "button,.b-icon,.b-fa,svg,input,.b-widget"
});
GridEditBase._$name = "GridEditBase";

// ../Grid/lib/Grid/feature/CellEdit.js
var editingActions = {
  finishAndEditNextRow: 1,
  finishAndEditPrevRow: 1,
  finishEditing: 1,
  cancelEditing: 1,
  finishAndEditNextCell: 1,
  finishAndEditPrevCell: 1
};
var allActions = {
  ...editingActions,
  startEditing: 1,
  finishAllSelected: 1
};
var scrollActionMethods = {
  cancel: "cancelEditing",
  complete: "finishEditing"
};
var CellEdit = class extends GridEditBase {
  // Default configuration
  static get defaultConfig() {
    return {
      /**
       * Set to `true` to select the field text when editing starts
       * @config {Boolean}
       * @default
       */
      autoSelect: true,
      /**
       * What action should be taken when focus moves leaves the cell editor, for example when clicking outside.
       * May be `'complete'` or `'cancel`'.
       * @config {'complete'|'cancel'}
       * @default
       */
      blurAction: "complete",
      /**
       * What action should be taken when the editor is scrolled out of view, for example when using a mousewheel
       * to scroll the grid.
       * May be `'complete'` or `'cancel`' or `null`.
       * @prp {'complete'|'cancel'|null}
       */
      scrollAction: null,
      /**
       * Set to `true` to have TAB key on the last cell (and ENTER anywhere in the last row) in the data set create
       * a new record and begin editing it at its first editable cell.
       *
       * If a customized {@link #config-keyMap} is used, this setting will affect the customized keys instead of
       * ENTER and TAB.
       *
       * If this is configured as an object, it is used as the default data value set for each new record.
       * @config {Boolean|Object}
       */
      addNewAtEnd: null,
      /**
       * Set to `true` to add record to the parent of the last record, when configured with {@link #config-addNewAtEnd}.
       * Only applicable when using a tree view and store.
       *
       * By default, it adds records to the root.
       * @prp {Boolean}
       * @default false
       */
      addToCurrentParent: false,
      /**
       * Set to `false` to not start editing next record when user presses enter inside a cell editor (or previous
       * record if SHIFT key is pressed). This is set to `false` when {@link #config-autoEdit} is `true`. Please
       * note that these key combinations could be different if a customized {@link #config-keyMap} is used.
       * @config {Boolean}
       * @default
       */
      editNextOnEnterPress: true,
      /**
       * Class to use as an editor. Default value: {@link Core.widget.Editor}
       * @config {Core.widget.Widget}
       * @typings {typeof Widget}
       * @internal
       */
      editorClass: Editor,
      // To edit a cell using a touch gesture, at least 300ms should have passed since last cell tap
      touchEditDelay: 300,
      focusCellAnimationDuration: false,
      /**
       * If set to `true` (which is default) this will make it possible to edit current column in multiple rows
       * simultaneously.
       *
       * This is achieved by:
       * 1. Select multiple rows or row's cells
       * 2. Start editing simultaneously as selecting the last row or cell
       * 3. When finished editing, press Ctrl+Enter to apply the new value to all selected rows.
       *
       * If a customized {@link #config-keyMap} is used, the Ctrl+Enter combination could map to something else.
       *
       * @config {Boolean}
       * @default
       */
      multiEdit: true,
      /**
       * See {@link #keyboard-shortcuts Keyboard shortcuts} for details
       * @config {Object<String,KeyMapConfig>}
       */
      keyMap: {
        Enter: ["startEditing", "finishAndEditNextRow"],
        "Ctrl+Enter": ["finishAllSelected", "finishEditing"],
        "Shift+Enter": "finishAndEditPrevRow",
        "Alt+Enter": "finishEditing",
        F2: ["startEditing", "finishEditing"],
        Escape: "cancelEditing",
        Tab: { handler: "finishAndEditNextCell", weight: 100 },
        "Shift+Tab": { handler: "finishAndEditPrevCell", weight: 100 }
      },
      /**
       * A callback which returns a `blurAction` value depending on the tap out event and the editing context.
       *
       * If specified, it is passed the mousedown event and the editing context object.
       * @internal
       */
      validateTapOut: null,
      /**
       * By default, when cell editing is active, the TAB key will move focus to edit the next cell with a configured editor.
       *
       * This will skip over cells which contain naturally focusable elements, such as buttons or input fields.
       *
       * To have these included in the tabbing, configure this as `true`.
       * @prp {Boolean}
       */
      tabToFocusables: null
    };
  }
  // Plugin configuration. This plugin chains some functions in Grid.
  static get pluginConfig() {
    return {
      assign: ["startEditing", "finishEditing", "cancelEditing"],
      before: ["onElementKeyDown", "onElementPointerUp"],
      chain: ["onElementClick", "bindStore"]
    };
  }
  //endregion
  //region Init
  construct(grid, config) {
    super.construct(grid, config);
    const me = this, gridListeners = {
      renderRows: "onGridRefreshed",
      thisObj: me
    };
    if (me.autoEdit && !("editNextOnEnterPress" in config)) {
      me.editNextOnEnterPress = false;
    }
    grid.ion(gridListeners);
    grid.rowManager.ion({
      changeTotalHeight: "onGridRefreshed",
      thisObj: me
    });
  }
  bindStore(store) {
    this.detachListeners("store");
    store.ion({
      name: "store",
      update: "onStoreUpdate",
      remove: "onStoreRemove",
      beforeSort: "onStoreBeforeSort",
      thisObj: this
    });
  }
  /**
   * Displays an OK / Cancel confirmation dialog box owned by the current Editor. This is intended to be
   * used by {@link Grid.column.Column#config-finalizeCellEdit} implementations. The returned promise resolves passing
   * `true` if the "OK" button is pressed, and `false` if the "Cancel" button is pressed. Typing `ESC` rejects.
   *
   * @param {Object} options An options object for what to show.
   * @param {String} [options.title] The title to show in the dialog header.
   * @param {String} [options.message] The message to show in the dialog body.
   * @param {String|Object} [options.cancelButton] A text or a config object to apply to the Cancel button.
   * @param {String|Object} [options.okButton] A text or config object to apply to the OK button.
   * @returns {Boolean}
   */
  async confirm(options) {
    let result = true;
    if (this.editorContext) {
      MessageDialog_default.owner = this.editorContext.editor.inputField;
      options.rootElement = this.grid.rootElement;
      result = await MessageDialog_default.confirm(options);
      MessageDialog_default.owner = null;
    }
    return result === MessageDialog_default.yesButton;
  }
  //endregion
  //region Editing
  /**
   * Internal function to create or get existing editor for specified cell.
   * @private
   * @param cellContext Cell to get or create editor for
   * @returns {Core.widget.Editor} An Editor container which displays the input field.
   * @category Internal
   */
  getEditorForCell({ id: id2, cell, column, columnId, editor }) {
    const me = this, {
      grid,
      editorClass
    } = me;
    let cellEditor = column.cellEditor, leftOffset = 0;
    if (column.editTargetSelector) {
      const editorTarget = cell.querySelector(column.editTargetSelector);
      leftOffset = editorTarget.offsetLeft;
    }
    editor.autoSelect = me.autoSelect;
    if (!(cellEditor == null ? void 0 : cellEditor.isEditor)) {
      cellEditor = column.data.cellEditor = editorClass.create(editorClass.mergeConfigs({
        type: editorClass.type,
        cls: "b-cell-editor",
        inputField: editor,
        blurAction: "none",
        invalidAction: column.invalidAction,
        completeKey: false,
        cancelKey: false,
        owner: grid,
        align: {
          align: "t0-t0",
          offset: [leftOffset, 0]
        },
        internalListeners: me.getEditorListeners(column.managedCellEditing),
        // Listen for cell edit control keys from the Editor
        onInternalKeyDown: me.onEditorKeydown.bind(me),
        // React editor wrapper code uses this flag to enable mouse events pass through to editor
        allowMouseEvents: editor.allowMouseEvents,
        instantUpdate: column.instantUpdate
      }, (cellEditor == null ? void 0 : cellEditor.floating) ? {
        // Apply other default values if floating
        align: {
          align: "t0-b0",
          allowTargetOut: false,
          minHeight: 100
          // arbitrary default main height
        },
        matchSize: false,
        scrollAction: "realign"
      } : {
        // Specifically not floating
        constrainTo: null
      }, cellEditor));
    }
    cellEditor.minHeight = grid.rowHeight;
    if (cellEditor.inputField !== editor) {
      cellEditor.remove(cellEditor.items[0]);
      cellEditor.add(editor);
    }
    const inputField = cellEditor.inputField, instantUpdate = column.instantUpdate && !editor.cellEditValueSetter;
    cellEditor.instantUpdate = instantUpdate;
    cellEditor.align.offset[0] = leftOffset;
    if (instantUpdate) {
      ObjectHelper.wrapProperty(editor, "value", null, (value2) => {
        const { editorContext } = me;
        if (editorContext) {
          const { record } = editorContext, { editorField } = editorContext.column;
          if (editorContext.editor.isValid && !ObjectHelper.isEqual(record.getValue(editorField), value2, true)) {
            if (!(inputField == null ? void 0 : inputField.isDateField) || (inputField == null ? void 0 : inputField._isPickerInput)) {
              record.setValue(editorField, value2);
              inputField._outstandingUpdate = false;
            } else {
              inputField._outstandingUpdate = true;
            }
          }
        }
      });
      editor.cellEditValueSetter = true;
    }
    Object.assign(cellEditor.element.dataset, {
      rowId: id2,
      columnId,
      field: column.editorField
    });
    cellEditor.inputField.revertOnEscape = column.revertOnEscape;
    return me.editor = cellEditor;
  }
  // Turned into function to allow overriding in Gantt, and make more configurable in general
  getEditorListeners(managedCellEditing) {
    return {
      focusOut: managedCellEditing ? "onEditorFocusOut" : null,
      focusIn: "onEditorFocusIn",
      start: "onEditorStart",
      beforeComplete: "onEditorBeforeComplete",
      complete: "onEditorComplete",
      beforeCancel: "onEditorBeforeCancel",
      cancel: "onEditorCancel",
      beforeHide: "onBeforeEditorHide",
      afterEdit: "onEditorAfterEdit",
      thisObj: this
    };
  }
  syncEditingReadOnly({ readOnly }) {
    if (this.isEditing && readOnly) {
      this.cancelEditing();
    }
  }
  onEditorStart({ source: editor }) {
    var _a5, _b2;
    const me = this, editorContext = me.editorContext = editor.cellEditorContext;
    if (editorContext) {
      const { grid } = me;
      if (me.triggerEvent !== "cellclick") {
        me.detachListeners("cellClickWhileEditing");
        grid.ion({
          name: "cellClickWhileEditing",
          cellclick: "onCellClickWhileEditing",
          thisObj: me
        });
      }
      (_a5 = me.removeEditingListeners) == null ? void 0 : _a5.call(me);
      (_b2 = me.removeScrollListener) == null ? void 0 : _b2.call(me);
      if (editorContext.column.managedCellEditing) {
        me.removeEditingListeners = GlobalEvents_default.addListener({
          globaltap: "onTapOut",
          thisObj: me
        });
      }
      if (me.scrollAction) {
        me.removeScrollListener = EventHelper.on({
          element: document,
          scroll: "onScroll",
          thisObj: me,
          capture: true
        });
      }
      grid.trigger("startCellEdit", { grid, editorContext });
    }
  }
  onScroll() {
    if (!DomHelper.isInView(this.editor.element)) {
      this[scrollActionMethods[this.scrollAction]]();
    }
  }
  onEditorBeforeComplete(context) {
    const { grid } = this, editor = context.source, editorContext = editor.cellEditorContext;
    context.grid = grid;
    context.editorContext = editorContext;
    editorContext.oldValue = editorContext.value;
    editorContext.value = editorContext.editor.inputField.value;
    return grid.trigger("beforeFinishCellEdit", context);
  }
  onEditorComplete({ source: editor }) {
    var _a5;
    const { grid } = this, editorContext = editor.cellEditorContext;
    if (!editorContext) {
      return;
    }
    editorContext.value = editor.inputField.value;
    if (editor.dataField.includes(".")) {
      const relationName = editor.dataField.split(".")[0];
      if ((_a5 = editor.record.constructor.relations) == null ? void 0 : _a5[relationName]) {
        grid.rowManager.refresh();
      }
    }
    grid.trigger("finishCellEdit", { grid, editorContext });
  }
  onEditorBeforeCancel() {
    const { editorContext } = this;
    return this.grid.trigger("beforeCancelCellEdit", { editorContext });
  }
  onEditorCancel({ event }) {
    const { editorContext, muteEvents, grid } = this;
    if (!muteEvents) {
      grid.trigger("cancelCellEdit", { grid, editorContext, event });
    }
  }
  onBeforeEditorHide({ source }) {
    var _a5, _b2;
    const me = this, {
      row,
      cell
    } = source.cellEditorContext;
    cell == null ? void 0 : cell.classList.remove("b-editing");
    row == null ? void 0 : row.removeCls("b-editing");
    me.detachListeners("cellClickWhileEditing");
    (_a5 = me.removeEditingListeners) == null ? void 0 : _a5.call(me);
    (_b2 = me.removeScrollListener) == null ? void 0 : _b2.call(me);
  }
  onEditorAfterEdit({ source }) {
    var _a5, _b2;
    const { editorContext } = this;
    source.cellEditorContext = this.editorContext = null;
    (_b2 = (_a5 = editorContext == null ? void 0 : editorContext.column) == null ? void 0 : _a5.onCellEditComplete) == null ? void 0 : _b2.call(_a5, editorContext);
  }
  /**
   * Find the next succeeding or preceding cell which is editable (column.editor != false)
   * @param {Grid.util.GridLocation} cellInfo
   * @param {Boolean} isForward
   * @returns {Object}
   * @private
   * @category Internal
   */
  getAdjacentEditableCell(cellInfo, isForward) {
    const { grid } = this, { store, columns } = grid, { visibleColumns } = columns;
    let rowId = cellInfo.id, { record } = cellInfo, column = columns.getAdjacentVisibleLeafColumn(cellInfo.columnId, isForward);
    while (rowId) {
      if (column) {
        if (this.tabToFocusables) {
          const cellContext = grid.normalizeCellContext({ record, column });
          if (cellContext.target !== cellContext.cell && !this.getEditingContext(cellContext)) {
            return cellContext;
          }
        }
        if (column.canEdit(record)) {
          return { id: rowId, columnId: column.id };
        }
        return this.getAdjacentEditableCell(grid.normalizeCellContext({ id: rowId, columnId: column.id }), isForward);
      } else {
        record = store.getAdjacent(cellInfo.id, isForward, false, true);
        rowId = record == null ? void 0 : record.id;
        if (record) {
          column = isForward ? visibleColumns[0] : visibleColumns[visibleColumns.length - 1];
        }
      }
    }
    return null;
  }
  /**
   * Adds a new, empty record at the end of the TaskStore with the initial
   * data specified by the {@link Grid.feature.CellEdit#config-addNewAtEnd} setting.
   *
   * @private
   * @async
   * @returns {Core.data.Model} Newly added record
   */
  async doAddNewAtEnd() {
    const newRecordConfig = typeof this.addNewAtEnd === "object" ? ObjectHelper.clone(this.addNewAtEnd) : {}, { grid: { store }, addToCurrentParent } = this;
    let record;
    if (store.tree && addToCurrentParent) {
      record = store.last.parent.appendChild(newRecordConfig);
    } else {
      record = store.add(newRecordConfig)[0];
    }
    await this.client.scrollRowIntoView(record, { animate: false, block: "nearest" });
    return record;
  }
  /**
   * Creates an editing context object for the passed cell context (target cell must be in the DOM).
   *
   * If the referenced cell is editable, a {@link Grid.util.GridLocation} will
   * be returned containing the following extra properties:
   *
   *     - editor
   *     - value
   *
   * If the referenced cell is _not_ editable, `false` will be returned.
   * @param {Object} cellContext an object which encapsulates a cell.
   * @param {String} cellContext.id The record id of the row to edit
   * @param {String} cellContext.columnId The column id of the column to edit
   * @returns {Grid.util.GridLocation|Boolean}
   * @private
   */
  getEditingContext(cellContext) {
    cellContext = this.grid.normalizeCellContext(cellContext);
    const { column, record } = cellContext, { editor } = column;
    if ((column == null ? void 0 : column.isVisible) && editor && record && !record.isSpecialRow && column.canEdit(record)) {
      const value2 = record ? column.getRawValue(record, true) : record;
      Object.assign(cellContext, {
        value: value2 === void 0 ? null : value2,
        editor
      });
      return cellContext;
    }
    return false;
  }
  startEditingFromKeyMap() {
    const { focusedCell } = this.grid, column = focusedCell == null ? void 0 : focusedCell.column;
    if (!this.disabled && (column == null ? void 0 : column.get("editor")) !== false) {
      return this.startEditing(focusedCell);
    }
    return false;
  }
  /**
   * Start editing specified cell. If no cellContext is given it starts with the first cell of the first visible row.
   * This function is exposed on Grid and can thus be called as `grid.startEditing(...)`
   * @param {GridLocationConfig} cellContext Cell specified in format { id: 'x', columnId/column/field: 'xxx' }. See
   * {@link Grid.view.Grid#function-getCell} for details.
   * @fires startCellEdit
   * @returns {Promise} Resolved promise returns`true` if editing has been started, `false` if an {@link Core.widget.Editor#event-beforeStart} listener
   * has vetoed the edit.
   * @category Editing
   * @on-owner
   */
  async startEditing(cellContext = {}) {
    var _a5, _b2, _c2, _d, _e;
    const me = this;
    if (me.disabled) {
      return false;
    }
    const { grid } = me;
    if (cellContext == null ? void 0 : cellContext.fromKeyMap) {
      cellContext = grid.focusedCell;
    }
    if (ObjectHelper.isEmpty(cellContext)) {
      cellContext.id = grid.firstVisibleRow.id;
    }
    if (grid.store.isTree && grid.features.tree) {
      const record2 = cellContext.id ? grid.store.getById(cellContext.id) : (_a5 = cellContext.record) != null ? _a5 : grid.store.getAt(cellContext.row);
      if (record2) {
        await grid.expandTo(record2);
      } else {
        return false;
      }
    }
    const editorContext = me.getEditingContext(cellContext);
    if (!editorContext) {
      return false;
    }
    if (me.editorContext) {
      if (editorContext.id === me.editorContext.id && editorContext.columnId === me.editorContext.columnId) {
        return;
      }
      if (me.cancelEditing() === false) {
        return false;
      }
    }
    if (!((_b2 = grid.focusedCell) == null ? void 0 : _b2.equals(editorContext))) {
      grid.focusCell(editorContext);
    }
    if (grid.trigger("beforeCellEditStart", { grid, editorContext }) === false) {
      return false;
    }
    const editor = editorContext.editor = me.getEditorForCell(editorContext), {
      row,
      cell,
      record
    } = editorContext;
    editor.inputField.highlightExternalChange = false;
    editor.cellEditorContext = editorContext;
    editor.render(cell);
    cell.classList.add("b-editing");
    row.addCls("b-editing");
    if (!await editor.startEdit({
      target: cell,
      field: editor.inputField.name || editorContext.column.editorField,
      value: editorContext.value,
      record
    })) {
      cell.classList.remove("b-editing");
      row.removeCls("b-editing");
    }
    (_d = (_c2 = editorContext.column).onCellEditStart) == null ? void 0 : _d.call(_c2, editorContext);
    (_e = me.onCellEditStart) == null ? void 0 : _e.call(me);
    return true;
  }
  /**
   * Cancel editing, hides the editor.
   * This function is exposed on Grid and can thus be called as `grid.cancelEditing(...)`
   * @param {Boolean} [silent] Pass true to prevent method from firing event
   * @fires cancelCellEdit
   * @returns {Boolean} `false` if the edit could not be canceled due to the editor's
   * {@link Core.widget.Editor#event-beforeCancel} event being prevented.
   * @category Editing
   * @on-owner
   */
  cancelEditing(silent = false, triggeredByEvent) {
    var _a5;
    const me = this, { editor } = me;
    if (!me.isEditing) {
      return;
    }
    if (silent.fromKeyMap) {
      triggeredByEvent = silent;
      silent = false;
    }
    me.muteEvents = silent;
    const cancelResult = editor.cancelEdit(triggeredByEvent);
    me.muteEvents = false;
    if (cancelResult === false) {
      editor.inputField.focus();
    } else {
      me.finishEditingPromise = false;
      (_a5 = me.afterCellEdit) == null ? void 0 : _a5.call(me);
    }
    return cancelResult;
  }
  /**
   * Finish editing, update the underlying record and hide the editor.
   * This function is exposed on Grid and can thus be called as `grid.finishEditing(...)`
   * @fires finishCellEdit
   * @category Editing
   * @returns {Promise} Resolved promise returns `false` if the edit could not be finished due to the value being invalid or the
   * Editor's `complete` event was vetoed.
   * @on-owner
   */
  async finishEditing(event) {
    var _a5;
    const me = this, { editorContext, grid } = me;
    let result = false;
    if (me.finishEditingPromise) {
      return me.finishEditingPromise;
    }
    if (editorContext) {
      const { column } = editorContext;
      if ((event == null ? void 0 : event.key) === "Enter" && !editorContext.editor.isEditor) {
        return false;
      }
      me.finishEditingPromise = editorContext.editor.completeEdit(column.bindCallback(column.finalizeCellEdit), event);
      result = await me.finishEditingPromise;
      if (grid.isDestroyed) {
        return;
      }
      if (grid.isSchedulerBase) {
        await grid.waitForAnimations();
      }
      me.finishEditingPromise = null;
      if (result) {
        await ((_a5 = me.afterCellEdit) == null ? void 0 : _a5.call(me));
      }
    }
    return result;
  }
  //endregion
  //region Events
  onStoreBeforeSort() {
    var _a5;
    const editor = (_a5 = this.editorContext) == null ? void 0 : _a5.editor;
    if (this.isEditing && !(editor == null ? void 0 : editor.isFinishing) && !editor.isValid) {
      return this.cancelEditing();
    }
  }
  /**
   * Realign editor if grid renders rows while editing is ongoing (as a result to autoCommit or WebSocket data received).
   * @private
   * @category Internal event handling
   */
  onGridRefreshed() {
    const me = this, {
      grid,
      editorContext
    } = me;
    if (editorContext && grid.isVisible && grid.focusedCell) {
      const { editor, cell } = editorContext;
      if (cell && DomHelper.isInView(cell) && !editor.isFinishing) {
        editorContext._cell = cell;
        GlobalEvents_default.suspendFocusEvents();
        editor.render(cell);
        editor.showBy(cell);
        editor.focus();
        GlobalEvents_default.resumeFocusEvents();
      } else if (!me.finishEditingPromise) {
        me.cancelEditing();
      }
    }
  }
  // Gets selected records or selected cells records
  get gridSelection() {
    return [...this.grid.selectedRows, ...this.grid.selectedCells];
  }
  // Tells keyMap what actions are available in certain conditions
  isActionAvailable({ actionName, event }) {
    const me = this;
    if (!allActions[actionName]) {
      return;
    }
    if (!me.disabled && !event.target.closest(".b-grid-header")) {
      if (me.isEditing) {
        if (actionName === "finishAllSelected") {
          return me.multiEdit && me.gridSelection.length > 1;
        } else if (editingActions[actionName]) {
          return true;
        }
      } else if (actionName === "startEditing") {
        return Boolean(me.grid.focusedCell && !DomHelper.isEditable(event.target));
      }
    }
    return false;
  }
  // Will copy edited field value to all selected records
  async finishAllSelected() {
    const me = this, { dataField, record, cellEditorContext } = me.editor;
    let { column } = cellEditorContext;
    if (await me.finishEditing() && !me.isDestroyed) {
      const value2 = record.getValue(dataField);
      for (let selected of me.gridSelection) {
        if (!selected.isModel) {
          column = selected.column;
          selected = selected.record;
        }
        if (selected !== record && column.canEdit(selected) !== false && /**
         * Fires for each selected record on the owning Grid before editing a range of selected cell values
         * @event beforeCellRangeEdit
         * @on-owner
         * @preventable
         * @param {Core.data.Model} record Current selected record from the range
         * @param {String} field The field being changed
         * @param {*} value The value being set
         * @returns {Boolean} `false` to prevent changing the current record
         */
        me.grid.trigger("beforeCellRangeEdit", {
          record: selected,
          field: column.editorField,
          value: value2
        }) !== false) {
          selected.set(column.editorField, value2);
        }
      }
    }
  }
  // Will finish editing and start editing next row (unless it's a touch device)
  // If addNewAtEnd, it will create a new row and edit that one if currently editing last row
  // NOTE: this method is overridden in the SchedulerPro CellEdit feature to support revisions.
  // When revisions support comes to Grid, we can merge them together. Changes made in this method
  // should likely be applied to the overridden method too
  async finishAndEditNextRow(event, previous = false) {
    var _a5;
    const me = this, { grid } = me, { record, column } = me.editorContext;
    let nextCell;
    const result = await me.finishEditing(event);
    if (result) {
      if (me.isDestroyed) {
        return;
      }
      if (!me.isEditing) {
        if (previous) {
          nextCell = grid.internalNextPrevRow(false, true, false);
          if (!nextCell && grid.splitFrom) {
            const otherGrid = grid.splitFrom;
            await otherGrid.startEditing({
              id: otherGrid.store.last.id,
              columnId: column.id
            });
          }
        } else {
          if (record === grid.store.last) {
            if ((_a5 = grid.features.lockRows) == null ? void 0 : _a5.enabled) {
              const otherGrid = grid.features.lockRows.subViews[0];
              if (otherGrid.store.count > 0) {
                await otherGrid.startEditing({
                  id: otherGrid.store.first.id,
                  columnId: column.id
                });
              }
            } else if (me.addNewAtEnd) {
              await me.doAddNewAtEnd();
            }
          }
          if (!me.isDestroyed) {
            nextCell = grid.internalNextPrevRow(true, true);
          }
        }
        if (nextCell && me.editNextOnEnterPress) {
          await me.startEditing(nextCell);
        }
      }
    }
    return result;
  }
  // Will finish editing and start editing previous row
  finishAndEditPrevRow(event) {
    return this.finishAndEditNextRow(event, true);
  }
  // Will finish editing and start editing next cell
  // If addNewAtEnd, it will create a new row and edit that one if currently editing last row
  async finishAndEditNextCell(event, previous = false) {
    var _a5, _b2, _c2, _d;
    const me = this, { grid } = me, { focusedCell } = grid;
    if (focusedCell && !me.finishEditingPromise) {
      const {
        columnIndex,
        record,
        actionTargets
      } = focusedCell;
      if (me.tabToFocusables && me.editorContext && !me.editorContext.editor.inputField) {
        if (DomHelper.getActiveElement(focusedCell.cell) !== (previous ? actionTargets[0] : actionTargets.at(-1))) {
          return false;
        }
      }
      let cellInfo = me.getAdjacentEditableCell(focusedCell, !previous);
      if (!cellInfo && !previous) {
        const currentEditableFinalizationResult = await me.finishEditing(event);
        if (currentEditableFinalizationResult === true) {
          if ((_a5 = grid.features.lockRows) == null ? void 0 : _a5.enabled) {
            const [otherGrid] = grid.subViews;
            if (otherGrid.store.count > 0) {
              const record2 = otherGrid.store.first, cellInfo2 = {
                record: record2,
                id: record2.id,
                columnId: otherGrid.columns.visibleColumns[0].id
              };
              if (otherGrid.features.cellEdit.getEditingContext(cellInfo2)) {
                await otherGrid.startEditing(cellInfo2);
              } else {
                await otherGrid.startEditing(me.getAdjacentEditableCell(cellInfo2));
              }
            }
          } else if (me.addNewAtEnd) {
            await me.doAddNewAtEnd();
          }
        }
      }
      let finalizationResult = true;
      if (me.isEditing) {
        finalizationResult = await me.finishEditing(event);
      }
      if (me.isDestroyed) {
        return;
      }
      cellInfo = me.getAdjacentEditableCell(new GridLocation({
        grid,
        record,
        columnIndex
      }), !previous);
      if (!cellInfo && previous && ((_b2 = grid.splitFrom) == null ? void 0 : _b2.features.lockRows) && record === grid.store.first) {
        const otherGrid = grid.splitFrom, record2 = otherGrid.store.last, column = otherGrid.columns.visibleColumns[otherGrid.columns.visibleColumns.length - 1], cellInfo2 = {
          record: record2,
          id: record2.id,
          columnId: column.id
        };
        if (otherGrid.features.cellEdit.getEditingContext(cellInfo2)) {
          await otherGrid.startEditing(cellInfo2);
        } else {
          await otherGrid.startEditing(me.getAdjacentEditableCell(cellInfo2, false));
        }
      }
      if (cellInfo) {
        if (finalizationResult) {
          if (cellInfo.target !== cellInfo.cell && me.tabToFocusables) {
            const actionTargets2 = grid.findFocusables(cellInfo.cell);
            cellInfo.editor = me.editor = me.getFakeEditor(cellInfo.target);
            actionTargets2[previous ? actionTargets2.length - 1 : 0].focus();
            me.editorContext = cellInfo;
          } else {
            grid.focusCell(cellInfo, {
              animate: me.focusCellAnimationDuration
            });
            if (!await me.startEditing(cellInfo)) {
              return me.finishAndEditNextCell(event, previous);
            }
          }
        } else {
        }
      } else if (grid.isNested && grid.owner && !((_d = (_c2 = grid.owner).catchFocus) == null ? void 0 : _d.call(_c2, { source: grid, navigationDirection: previous ? "up" : "down", editing: true }))) {
        grid.onTab(event);
      }
    }
  }
  // Used when beginning fake editing of an actionable cell.
  // The Editor is a disposable object. All it needs is the cancelEdit and completeEdit methods.
  getFakeEditor(target) {
    const me = this, cancelOrComplete = () => {
      me.editorContext = me.editor = null;
      return true;
    };
    return {
      element: target,
      completeEdit: cancelOrComplete,
      cancelEdit: cancelOrComplete,
      isFinishing: true
    };
  }
  // Will finish editing and start editing next cell
  finishAndEditPrevCell(event) {
    return this.finishAndEditNextCell(event, true);
  }
  // Handles autoedit
  async onElementKeyDown(event) {
    const me = this, { grid } = me, { focusedCell } = grid;
    if (event.handled || !me.autoEdit || me.isEditing || !focusedCell || focusedCell.isActionable && DomHelper.isEditable(event.target) || event.ctrlKey) {
      return;
    }
    const { key } = event, isDelete = event.key === "Delete" || event.key === "Backspace", { gridSelection } = isDelete ? me : {}, isMultiDelete = me.multiEdit && (gridSelection == null ? void 0 : gridSelection.length) > 1;
    if ((key.length <= 1 || isDelete && !isMultiDelete) && await me.startEditing(focusedCell)) {
      const { inputField } = me.editor, { input } = inputField;
      if (input) {
        inputField.internalOnKeyEvent(event);
        if (!event.defaultPrevented) {
          input.value = isDelete ? "" : key;
          inputField.internalOnInput(event);
        }
      }
      event.preventDefault();
    } else if (isMultiDelete) {
      if (grid.trigger("beforeCellRangeDelete", { grid, gridSelection }) !== false) {
        for (const selected of gridSelection) {
          if (selected.isModel) {
            grid.columns.visibleColumns.forEach((col) => {
              if (col.canEdit(selected)) {
                selected.set(col.editorField, null);
              }
            });
          } else if (selected.column.canEdit(selected.record)) {
            selected.record.set(selected.column.editorField, null);
          }
        }
      }
    }
  }
  // Prevents keys which the Grid handles from bubbling to the grid while editing
  onEditorKeydown(event) {
    if (event.key && event.key.length !== 1 && this.grid.matchKeyMapEntry(event) && !this.grid.matchKeyMapEntry(event, this.keyMap)) {
      if (!event.key.startsWith("Arrow") && event.key !== "Backspace" && event.key !== "End") {
        event.preventDefault();
      }
      event.handled = true;
      event.stopPropagation();
      return false;
    }
  }
  /**
   * Cancel editing on widget focusout
   * @private
   */
  async onEditorFocusOut(event) {
    const me = this, {
      grid,
      editor,
      editorContext
    } = me, toCell = new GridLocation(event.relatedTarget), isEditableCellClick = toCell.grid === grid && me.getEditingContext(toCell);
    if (editorContext && !editor.isFinishing && editor.owns(event._target)) {
      if (me.blurAction === "cancel" || !grid.store.includes(editorContext.record)) {
        me.cancelEditing(void 0, event);
      } else if (!me.finishEditingPromise && (me.triggerEvent === "cellclick" || me.triggerEvent !== "cellclick" && !isEditableCellClick)) {
        await me.finishEditing(event);
      }
    }
  }
  onEditorFocusIn(event) {
    const widget = event.toWidget;
    if (widget === this.editor.inputField) {
      if (this.autoSelect && widget.selectAll && !widget.readOnly && !widget.disabled) {
        widget.selectAll();
      }
    }
  }
  /**
   * Cancel edit on touch outside of grid for mobile Safari (focusout not triggering unless you touch something focusable)
   * @private
   */
  async onTapOut({ event }) {
    const me = this, { target } = event;
    if (!target._shadowRoot && !me.editor.owns(target) && (!me.grid.bodyContainer.contains(target) || event.button)) {
      const validateTapOut = me.resolveCallback(me.validateTapOut, me, false), blurAction = (validateTapOut == null ? void 0 : validateTapOut.handler.call(validateTapOut.thisObj, event, me.editorContext)) || me.blurAction;
      me.editingStoppedByTapOutside = true;
      if (blurAction === "cancel") {
        me.cancelEditing(void 0, event);
      } else {
        await me.finishEditing(event);
      }
      delete me.editingStoppedByTapOutside;
    }
  }
  /**
   * Finish editing if clicking below rows (only applies when grid is higher than rows).
   * Also finish if event target is the subgrid which can happen if the pointer is moved during mouse down.
   * @private
   * @category Internal event handling
   */
  async onElementClick(event) {
    if (event.target.matches(".b-grid-body-container, .b-grid-subgrid") && this.editorContext) {
      await this.finishEditing(event);
    }
  }
  //endregion
};
//region Config
__publicField(CellEdit, "$name", "CellEdit");
CellEdit._$name = "CellEdit";
GridFeatureManager.registerFeature(CellEdit, true);

// ../Grid/lib/Grid/feature/CellMenu.js
var CellMenu = class extends ContextMenuBase {
  static get defaultConfig() {
    return {
      /**
       * A function called before displaying the menu that allows manipulations of its items.
       * Returning `false` from this function prevents the menu being shown.
       *
       * ```javascript
       * features : {
       *     cellMenu : {
       *         processItems({ items, record, column }) {
       *             // Add or hide existing items here as needed
       *             items.myAction = {
       *                 text   : 'Cool action',
       *                 icon   : 'b-fa b-fa-fw b-fa-ban',
       *                 onItem : () => console.log(`Clicked ${record.name}`),
       *                 weight : 1000 // Move to end
       *             };
       *
       *             if (!record.allowDelete) {
       *                 items.removeRow.hidden = true;
       *             }
       *         }
       *     }
       * },
       * ```
       *
       * @config {Function}
       * @param {Object} context An object with information about the menu being shown.
       * @param {Grid.feature.CellMenu} context.feature A reference to this feature.
       * @param {Event} context.domEvent The initiating event.
       * @param {Event} context.event DEPRECATED: The initiating event.
       * @param {Number[]} context.point The client `X` and `Y` position of the initiating event.
       * @param {HTMLElement} context.targetElement The target to which the menu is being applied.
       * @param {Core.data.Model} context.record The record representing the current row.
       * @param {Grid.column.Column} context.column The current column.
       * @param {Object<String,MenuItemConfig|Boolean>} context.items An object containing the
       *   {@link Core.widget.MenuItem menu item} configs keyed by their id
       * @returns {Boolean|null} Returning `false` from this function prevents the menu being shown
       * @preventable
       */
      processItems: null,
      /**
       * {@link Core.widget.Menu} items object containing named child menu items to apply to the feature's
       * provided context menu.
       *
       * This may add extra items as below, but you can also configure, or remove any of the default items by
       * configuring the name of the item as `null`:
       *
       * ```javascript
       * features : {
       *     cellMenu : {
       *         // This object is applied to the Feature's predefined default items
       *         items : {
       *             switchToDog : {
       *                 text : 'Dog',
       *                 icon : 'b-fa b-fa-fw b-fa-dog',
       *                 onItem({record}) {
       *                     record.dog = true;
       *                     record.cat = false;
       *                 },
       *                 weight : 500     // Make this second from end
       *             },
       *             switchToCat : {
       *                 text : 'Cat',
       *                 icon : 'b-fa b-fa-fw b-fa-cat',
       *                 onItem({record}) {
       *                     record.dog = false;
       *                     record.cat = true;
       *                 },
       *                 weight : 510     // Make this sink to end
       *             },
       *             removeRow : {
       *                 // Change icon for the delete item
       *                 icon : 'b-fa b-fa-times'
       *             },
       *             secretItem : null
       *         }
       *     }
       * },
       * ```
       *
       * @config {Object<String,MenuItemConfig|Boolean|null>}
       */
      items: null,
      type: "cell"
      /**
       * See {@link #keyboard-shortcuts Keyboard shortcuts} for details
       * @config {Object<String,KeyMapConfig>} keyMap
       */
    };
  }
  static get pluginConfig() {
    const config = super.pluginConfig;
    config.chain.push("populateCellMenu");
    return config;
  }
  //endregion
  //region Events
  /**
   * This event fires on the owning grid before the context menu is shown for a cell.
   * Allows manipulation of the items to show in the same way as in the {@link #config-processItems}.
   *
   * Returning `false` from a listener prevents the menu from being shown.
   *
   * @event cellMenuBeforeShow
   * @preventable
   * @on-owner
   * @param {Grid.view.Grid} source The grid
   * @param {Core.widget.Menu} menu The menu
   * @param {Object<String,MenuItemConfig>} items Menu item configs
   * @param {Grid.column.Column} column Column
   * @param {Core.data.Model} record Record
   */
  /**
   * This event fires on the owning grid after the context menu is shown for a cell.
   * @event cellMenuShow
   * @on-owner
   * @param {Grid.view.Grid} source The grid
   * @param {Core.widget.Menu} menu The menu
   * @param {Object<String,MenuItemConfig>} items Menu item configs
   * @param {Grid.column.Column} column Column
   * @param {Core.data.Model} record Record
   */
  /**
   * This event fires on the owning grid when an item is selected in the cell context menu.
   * @event cellMenuItem
   * @on-owner
   * @param {Grid.view.Grid} source The grid
   * @param {Core.widget.Menu} menu The menu
   * @param {Core.widget.MenuItem} item Selected menu item
   * @param {Grid.column.Column} column Column
   * @param {Core.data.Model} record Record
   */
  /**
   * This event fires on the owning grid when a check item is toggled in the cell context menu.
   * @event cellMenuToggleItem
   * @on-owner
   * @param {Grid.view.Grid} source The grid
   * @param {Core.widget.Menu} menu The menu
   * @param {Core.widget.MenuItem} item Selected menu item
   * @param {Grid.column.Column} column Column
   * @param {Core.data.Model} record Record
   * @param {Boolean} checked Checked or not
   */
  //endregion
  //region Menu handlers
  showContextMenu(eventParams) {
    const me = this, {
      cellSelector,
      event
    } = eventParams;
    me.client.focusCell(cellSelector, {
      doSelect: !me.client.selectedRecordCollection.includes(cellSelector.id),
      event
    });
    super.showContextMenu(eventParams);
  }
  shouldShowMenu({ column }) {
    return column && column.enableCellContextMenu !== false;
  }
  getDataFromEvent(event) {
    const cellData = this.client.getCellDataFromEvent(event);
    if (cellData) {
      return ObjectHelper.assign(super.getDataFromEvent(event), cellData);
    }
  }
  beforeContextMenuShow({ record, items: items2, column }) {
    if (column.cellMenuItems === false) {
      return false;
    }
    if (!record || record.isSpecialRow) {
      items2.removeRow = false;
    }
  }
  //endregion
  //region Getters/Setters
  populateCellMenu({ items: items2, column, record }) {
    const { client } = this;
    if (column == null ? void 0 : column.cellMenuItems) {
      ObjectHelper.merge(items2, column.cellMenuItems);
    }
    if (!client.readOnly) {
      items2.removeRow = {
        text: "L{removeRow}",
        localeClass: this,
        icon: "b-fw-icon b-icon-trash",
        cls: "b-separator",
        weight: 100,
        disabled: record.readOnly,
        onItem: () => {
          var _a5, _b2;
          const store = (_b2 = (_a5 = client.features.treeGroup) == null ? void 0 : _a5.originalStore) != null ? _b2 : client.store;
          if (client.isCellSelected({ record, column }, true)) {
            store.remove(client.selectedRecords.filter((r) => !r.readOnly).map((r) => r.$original));
          } else {
            store.remove(record);
          }
        }
      };
    }
  }
  get showMenu() {
    return true;
  }
  /**
   * Shows the context menu for the provided cell.
   * @param {GridLocationConfig|Grid.util.GridLocation} cell The cell descriptor to show the menu for.
   * @param {Object} [options]
   * @param {HTMLElement} [options.targetElement] Element to align context menu to.
   * If provided menu will be aligned to the center of the target element.
   * If omitted, the context menu will be centered relative to the cell element.
   */
  showMenuFor(cellSelector, { targetElement } = {}) {
    cellSelector = this.client.normalizeCellContext(cellSelector);
    DomHelper.triggerMouseEvent(targetElement || this.client.getCell(cellSelector), this.triggerEvent);
  }
  //endregion
};
//region Config
__publicField(CellMenu, "$name", "CellMenu");
CellMenu.featureClass = "";
CellMenu._$name = "CellMenu";
GridFeatureManager.registerFeature(CellMenu, true, ["Grid", "Scheduler"]);
GridFeatureManager.registerFeature(CellMenu, false, ["Gantt"]);

// ../Grid/lib/Grid/feature/CellTooltip.js
var CellTooltip = class extends InstancePlugin {
  //endregion
  // region Init
  construct(grid, config) {
    super.construct(grid, this.processConfig(config));
  }
  initTip() {
    const me = this;
    me.tip = Tooltip.new({
      forElement: me.client.element,
      forSelector: ".b-grid-row:not(.b-group-row) .b-grid-cell, .b-grid-merged-cells",
      hoverDelay: 1e3,
      trackMouse: false,
      cls: "b-celltooltip-tip",
      getHtml: me.getTooltipContent.bind(me),
      internalListeners: {
        pointerOver: "onPointerOver",
        thisObj: me
      },
      // eslint-disable-next-line bryntum/no-listeners-in-lib
      listeners: me.configuredListeners
    }, me.initialConfig);
    me.client.ion({
      startCellEdit: me.tip.hide,
      thisObj: me.tip
    });
    me.relayEvents(me.tip, ["beforeShow", "show"]);
  }
  onPointerOver({ target }) {
    const column = this.client.getColumnFromElement(target);
    return column.tooltipRenderer !== false && Boolean(column.tooltipRenderer || this.tooltipRenderer);
  }
  // CellTooltip feature handles special config cases, where user can supply a function to use as tooltipRenderer
  // instead of a normal config object
  processConfig(config) {
    if (typeof config === "function") {
      return {
        tooltipRenderer: config
      };
    }
    return config;
  }
  // override setConfig to process config before applying it (used mainly from ReactGrid)
  setConfig(config) {
    super.setConfig(this.processConfig(config));
  }
  doDestroy() {
    var _a5;
    (_a5 = this.tip) == null ? void 0 : _a5.destroy();
    super.doDestroy();
  }
  doDisable(disable) {
    var _a5;
    if (disable) {
      this.tip = (_a5 = this.tip) == null ? void 0 : _a5.destroy();
    } else {
      this.initTip();
    }
    super.doDisable(disable);
  }
  //endregion
  //region Content
  /**
   * Called from Tooltip to populate it with html.
   * @private
   */
  getTooltipContent({ tip, activeTarget: cellElement, event }) {
    const me = this, record = me.client.getRecordFromElement(cellElement), column = me.client.getColumnFromElement(cellElement), arg = { cellElement, record, column, event, tip, cellTooltip: me };
    let result;
    if (!me.forSelector && record === me.lastRecord && record.generation === me.lastRecordGeneration && column === me.lastColumn) {
      return me.tip._html;
    }
    me.lastRecord = record;
    me.lastRecordGeneration = record.generation;
    me.lastColumn = column;
    if (column.tooltipRenderer) {
      result = column.tooltipRenderer(arg);
    } else if (me.tooltipRenderer && column.tooltipRenderer !== false) {
      result = me.tooltipRenderer(arg);
    }
    if (Objects.isPromise(result)) {
      me.lastRecord = me.lastRecordGeneration = me.lastColumn = null;
    }
    if (!result) {
      tip.hide();
    }
    return result;
  }
  //endregion
};
//region Config
__publicField(CellTooltip, "$name", "CellTooltip");
__publicField(CellTooltip, "configurable", {
  /**
   * Function called to generate the HTML content for the cell tooltip.
   * The function should return a string (your HTML), or a Promise yielding a string (for remotely loaded
   * content)
   * @prp {Function}
   * @param {Object} context
   * @param {HTMLElement} context.cellElement The cell element
   * @param {Core.data.Model} context.record The row record
   * @param {Grid.column.Column} context.column The column
   * @param {Core.widget.Tooltip} context.tip The Tooltip instance
   * @param {Grid.feature.CellTooltip} context.cellTooltip The feature
   * @param {Event} context.event The raw DOM event
   * @returns {String|Promise|VueConfig}
   * @typings {String|Promise<String>|VueConfig}
   */
  tooltipRenderer: null
});
CellTooltip._$name = "CellTooltip";
GridFeatureManager.registerFeature(CellTooltip);

// ../Grid/lib/Grid/feature/ColumnAutoWidth.js
var storeListenerName = "store";
var ColumnAutoWidth = class extends Delayable_default(InstancePlugin) {
  //endregion
  //region Internals
  static get pluginConfig() {
    return {
      after: {
        bindStore: "bindStore",
        unbindStore: "unbindStore",
        renderRows: "syncAutoWidthColumns",
        onInternalResize: "onInternalResize"
      },
      assign: [
        "columnAutoWidthPending",
        "syncAutoWidthColumns"
      ]
    };
  }
  construct(config) {
    super.construct(config);
    const { store } = this;
    store && this.bindStore(store);
  }
  doDestroy() {
    this.unbindStore();
    super.doDestroy();
  }
  get store() {
    return this.client.$store;
  }
  bindStore(store) {
    this.lastSync = null;
    store.ion({
      name: storeListenerName,
      [`change${this.client.asyncEventSuffix}`]: "onStoreChange",
      thisObj: this
    });
  }
  unbindStore() {
    this.detachListeners(storeListenerName);
  }
  get columnAutoWidthPending() {
    return this.lastSync === null || this.hasTimeout("syncAutoWidthColumns");
  }
  onStoreChange({ action }) {
    if (action !== "move" && this.client.isPainted) {
      const me = this, { cellEdit } = me.client.features;
      ++me.storeGeneration;
      if (action === "filter" || (cellEdit == null ? void 0 : cellEdit.isEditing) && !cellEdit.editingStoppedByTapOutside) {
        me.syncAutoWidthColumns();
      } else if (!me.client.refreshSuspended) {
        me.setTimeout({ fn: "syncAutoWidthColumns", delay: me.delay, cancelOutstanding: true });
      } else {
        me.client.rowManager.once("renderDone", () => me.syncAutoWidthColumns());
      }
    }
  }
  // Handle scenario with Grid being inside DIV with display none, and no width. Sync column widths after being shown
  onInternalResize(element, newWidth, newHeight, oldWidth) {
    if (oldWidth === 0) {
      this.lastSync = null;
      this.syncAutoWidthColumns();
    }
  }
  syncAutoWidthColumns() {
    var _a5, _b2;
    const { client } = this;
    if (this.disabled || client.store.count === 0 || client.rowManager.rows.length === 0 || client.splitFrom && !((_b2 = (_a5 = client.splitFrom.features) == null ? void 0 : _a5.lockRows) == null ? void 0 : _b2.enabled)) {
      return;
    }
    this.doSyncAutoWidthColumns();
  }
  doSyncAutoWidthColumns() {
    const me = this, {
      client,
      storeGeneration
    } = me;
    if (client.splitFrom && !client.usesLockRows || !client.isPainted) {
      return;
    }
    if (me.lastSync !== storeGeneration) {
      me.lastSync = storeGeneration;
      let autoWidth, resizingColumns;
      for (const column of client.columns.visibleColumns) {
        autoWidth = column.autoWidth;
        if (autoWidth) {
          if (autoWidth === true) {
            autoWidth = me.default;
          }
          client.resizingColumns = resizingColumns = true;
          column.resizeToFitContent(autoWidth);
        }
      }
      if (resizingColumns) {
        client.resizingColumns = false;
        client.afterColumnsResized();
      }
    }
    if (me.hasTimeout("syncAutoWidthColumns")) {
      me.clearTimeout("syncAutoWidthColumns");
    }
  }
  //endregion
};
__publicField(ColumnAutoWidth, "$name", "ColumnAutoWidth");
//region Config
__publicField(ColumnAutoWidth, "configurable", {
  /**
   * The default `autoWidth` option for columns with `autoWidth: true`. This can
   * be a single number for the minimum column width, or an array of two numbers
   * for the `[minWidth, maxWidth]`.
   * @config {Number|Number[]}
   */
  default: null,
  /**
   * The amount of time (in milliseconds) to delay after a store modification
   * before synchronizing `autoWidth` columns.
   * @config {Number}
   * @default
   */
  delay: 0
});
ColumnAutoWidth.prototype.storeGeneration = 0;
ColumnAutoWidth._$name = "ColumnAutoWidth";
GridFeatureManager.registerFeature(ColumnAutoWidth, true);

// ../Grid/lib/Grid/feature/ColumnDragToolbar.js
var ColumnDragToolbar = class extends Delayable_default(InstancePlugin) {
  // Plugin configuration. This plugin chains some of the functions in Grid
  static get pluginConfig() {
    return {
      after: ["render"]
    };
  }
  //endregion
  //region Init
  construct(grid, config) {
    if (grid.features.columnReorder) {
      grid.features.columnReorder.ion({ beforeDestroy: "onColumnReorderBeforeDestroy", thisObj: this });
    }
    this.grid = grid;
    super.construct(grid, config);
  }
  doDestroy() {
    const me = this;
    if (me.grid.features.columnReorder && !me.grid.features.columnReorder.isDestroyed) {
      me.detachFromColumnReorder();
    }
    me.element && me.element.remove();
    me.element = null;
    super.doDestroy();
  }
  doDisable(disable) {
    if (this.initialized) {
      if (disable) {
        this.detachFromColumnReorder();
      } else {
        this.init();
      }
    }
    super.doDisable(disable);
  }
  init() {
    const me = this, grid = me.grid;
    if (!grid.features.columnReorder) {
      return;
    }
    me.reorderDetacher = grid.features.columnReorder.ion({
      gridheaderdragstart({ context }) {
        const column = grid.columns.getById(context.element.dataset.columnId);
        me.showToolbar(column);
      },
      gridheaderdrag: ({ context }) => me.onDrag(context),
      gridheaderabort: () => {
        me.hideToolbar();
      },
      gridHeaderDrop: me.onDrop,
      thisObj: me
    });
    me.initialized = true;
  }
  onColumnReorderBeforeDestroy() {
    this.detachFromColumnReorder();
  }
  detachFromColumnReorder() {
    const me = this;
    me.grid.features.columnReorder.un("beforedestroy", me.onColumnReorderBeforeDestroy, me);
    me.reorderDetacher && me.reorderDetacher();
    me.reorderDetacher = null;
  }
  /**
   * Initializes this feature on grid render.
   * @private
   */
  render() {
    if (!this.initialized) {
      this.init();
    }
  }
  //endregion
  //region Toolbar
  showToolbar(column) {
    const me = this, buttons = me.grid.getColumnDragToolbarItems(column, []), groups = [];
    me.clearTimeout(me.buttonHideTimer);
    buttons.forEach((button) => {
      button.text = button.localeClass.L(button.text);
      let group = groups.find((group2) => group2.text === button.group);
      if (!group) {
        group = {
          text: button.localeClass.L(button.group),
          buttons: []
        };
        groups.push(group);
      }
      group.buttons.push(button);
    });
    me.element = DomHelper.append(me.grid.element, me.template(groups));
    me.groups = groups;
    me.buttons = buttons;
    me.column = column;
  }
  async hideToolbar() {
    const me = this, element = me.element;
    if (element) {
      element.classList.add("b-remove");
      await EventHelper.waitForTransitionEnd({
        element,
        mode: "animation",
        thisObj: me.client
      });
      element.remove();
      me.element = null;
    }
  }
  //endregion
  //region Events
  onDrag(info) {
    var _a5;
    const me = this;
    if (info.dragProxy.getBoundingClientRect().top - me.grid.element.getBoundingClientRect().top > 100) {
      me.element.classList.add("b-closer");
    } else {
      me.element.classList.remove("b-closer");
    }
    if (me.hoveringButton) {
      me.hoveringButton.classList.remove("b-hover");
      me.hoveringButton = null;
    }
    if ((_a5 = info.targetElement) == null ? void 0 : _a5.closest(".b-columndragtoolbar")) {
      me.element.classList.add("b-hover");
      const button = info.targetElement.closest(".b-columndragtoolbar  .b-target-button:not([data-disabled=true])");
      if (button) {
        button.classList.add("b-hover");
        me.hoveringButton = button;
      }
    } else {
      me.element.classList.remove("b-hover");
    }
  }
  onDrop({ context }) {
    const me = this, { targetElement } = context;
    if (targetElement && targetElement.matches(".b-columndragtoolbar .b-target-button:not([data-disabled=true])")) {
      const button = me.buttons.find((button2) => button2.ref === targetElement.dataset.ref);
      if (button) {
        targetElement.classList.add("b-activate");
        me.buttonHideTimer = me.setTimeout(() => {
          me.hideToolbar();
          button.onDrop({ column: me.column });
        }, 100);
      }
    } else {
      me.hideToolbar();
    }
  }
  //endregion
  template(groups) {
    return TemplateHelper.tpl`
            <div class="b-columndragtoolbar">
            <div class="b-title"></div>
            ${groups.map((group) => TemplateHelper.tpl`
                <div class="b-group">
                    <div class="b-buttons">
                    ${group.buttons.map((btn) => TemplateHelper.tpl`
                        <div class="b-target-button" data-ref="${btn.ref}" data-disabled="${btn.disabled}">
                            <i class="${btn.icon}"></i>
                            ${btn.text}
                        </div>
                    `)}
                    </div>
                    <div class="b-title">${group.text}</div>
                </div>
            `)}
            </div>`;
  }
};
//region Config
__publicField(ColumnDragToolbar, "$name", "ColumnDragToolbar");
ColumnDragToolbar.featureClass = "b-hascolumndragtoolbar";
ColumnDragToolbar._$name = "ColumnDragToolbar";
GridFeatureManager.registerFeature(ColumnDragToolbar, BrowserHelper.isTouchDevice);

// ../Grid/lib/Grid/feature/ColumnPicker.js
var ColumnPicker = class extends InstancePlugin {
  static get pluginConfig() {
    return {
      chain: ["populateHeaderMenu", "getColumnDragToolbarItems"]
    };
  }
  get grid() {
    return this.client;
  }
  //endregion
  //region Context menu
  /**
   * Get menu items, either a straight list of columns or sub menus per subgrid
   * @private
   * @param columnStore Column store to traverse
   * @returns {MenuItemConfig[]} Menu item configs
   */
  getColumnPickerItems(columnStore) {
    const me = this, { createColumnsFromModel } = me;
    let result;
    if (me.groupByRegion) {
      result = me.grid.regions.map((region) => {
        const columns = me.grid.getSubGrid(region).columns.topColumns;
        return {
          text: StringHelper.capitalize(region),
          menu: me.buildColumnMenu(columns),
          disabled: columns.length === 0,
          region
        };
      });
      if (createColumnsFromModel) {
        result.push({
          text: me.L("L{newColumns}"),
          menu: me.createAutoColumnItems()
        });
      }
    } else if (me.groupByTag) {
      const tags = {};
      columnStore.topColumns.forEach((column) => {
        column.tags && Array.isArray(column.tags) && column.hideable !== false && column.tags.forEach((tag) => {
          if (!tags[tag]) {
            tags[tag] = 1;
          }
        });
      });
      result = Object.keys(tags).sort().map((tag) => ({
        text: StringHelper.capitalize(tag),
        menu: me.buildColumnMenu(me.getColumnsForTag(tag)),
        tag,
        onBeforeSubMenu: ({ item, itemEl }) => {
          me.refreshTagMenu(item, itemEl);
        }
      }));
      if (createColumnsFromModel) {
        result.push({
          text: me.L("L{newColumns}"),
          menu: me.createAutoColumnItems()
        });
      }
    } else {
      result = me.buildColumnMenu(columnStore.topColumns);
      if (createColumnsFromModel) {
        result.items.push(...ObjectHelper.transformNamedObjectToArray(me.createAutoColumnItems()));
      }
    }
    return result;
  }
  createAutoColumnItems() {
    const me = this, { grid } = me, {
      columns,
      store
    } = grid, { modelClass } = store, { allFields } = modelClass, result = {};
    for (let i = 0, { length } = allFields; i < length; i++) {
      const field = allFields[i], fieldName = field.name;
      if (!columns.get(fieldName)) {
        if (!field.internal) {
          result[fieldName] = {
            text: field.text || StringHelper.separate(field.name),
            checked: false,
            onToggle: (event) => {
              const column = columns.get(fieldName);
              if (column) {
                column[event.checked ? "show" : "hide"]();
              } else {
                grid._suspendRenderContentsOnColumnsChanged = true;
                columns.add(columns.generateColumnForField(field, {
                  region: me.forColumn.region
                }), true);
                grid._suspendRenderContentsOnColumnsChanged = false;
                grid.renderContents();
              }
              event.bubbles = false;
            }
          };
        }
      }
    }
    return result;
  }
  /**
   * Get all columns that has the specified tag.
   * @private
   * @param tag
   * @returns {Grid.column.Column[]}
   */
  getColumnsForTag(tag) {
    return this.grid.columns.records.filter(
      (column) => column.tags && Array.isArray(column.tags) && column.tags.includes(tag) && column.hideable !== false
    );
  }
  /**
   * Refreshes checked status for a tag menu. Needed since columns can appear under multiple tags.
   * @private
   */
  refreshTagMenu(item, itemEl) {
    const columns = this.getColumnsForTag(item.tag);
    columns.forEach((column) => {
      const subItem = item.items.find((subItem2) => subItem2.column === column);
      if (subItem)
        subItem.checked = column.hidden !== true;
    });
  }
  /**
   * Traverses columns to build menu items for the column picker.
   * @private
   */
  buildColumnMenu(columns) {
    let currentRegion = columns.length > 0 && columns[0].region;
    const { grid } = this, items2 = columns.reduce((items3, column) => {
      if (column.hideable !== false) {
        const visibleInRegion = grid.columns.visibleColumns.filter((col) => col.region === column.region), checked = column.hidden !== true, itemConfig = {
          grid,
          column,
          text: column.htmlEncodeHeaderText ? column.text : DomHelper.stripTags(column.text),
          checked,
          disabled: checked && visibleInRegion.length === 1,
          cls: column.region !== currentRegion ? "b-separator" : "",
          ariaLabel: this.L(checked ? "L{ColumnPicker.hideName}" : "L{ColumnPicker.showName}", column)
        };
        currentRegion = column.region;
        if (column.children && !column.isCollapsible) {
          itemConfig.menu = this.buildColumnMenu(column.children);
        }
        items3.push(itemConfig);
      }
      return items3;
    }, []);
    return {
      cls: this.menuCls,
      items: items2
    };
  }
  /**
   * Populates the header context menu items.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Object<String,MenuItemConfig|Boolean|null>} options.items A named object to describe menu items
   * @internal
   */
  populateHeaderMenu({ column, items: items2 }) {
    const me = this, { columns } = me.grid;
    me.forColumn = column;
    if (column.showColumnPicker !== false && columns.some((col) => col.hideable)) {
      items2.columnPicker = {
        text: "L{columnsMenu}",
        localeClass: me,
        icon: "b-fw-icon b-icon-columns",
        cls: "b-separator",
        weight: 200,
        menu: me.getColumnPickerItems(columns),
        onToggle: me.onColumnToggle,
        disabled: me.disabled
      };
    }
    if (column.hideable !== false && !column.parent.isCollapsible) {
      items2.hideColumn = {
        text: "L{hideColumn}",
        localeClass: me,
        icon: "b-fw-icon b-icon-hide-column",
        weight: 210,
        disabled: !column.allowDrag || me.disabled,
        onItem: () => column.hide()
      };
    }
  }
  /**
   * Handler for column hide/show menu checkitems.
   * @private
   * @param {Object} event The {@link Core.widget.MenuItem#event-toggle} event.
   */
  onColumnToggle({ menu, item, checked }) {
    var _a5, _b2;
    if (Boolean(item.column.hidden) !== !checked) {
      item.column[checked ? "show" : "hide"]();
      item.ariaLabel = this.L(`L{HeaderMenu.${checked ? "hide" : "show"}Name}`, item.column);
      const { grid, column } = item, { columns, features } = grid, siblingItems = menu.items, otherHideableCols = columns.visibleColumns.filter((col) => col.region === item.column.region && col.showColumnPicker !== false), hideItem = ((_a5 = features.headerMenu) == null ? void 0 : _a5.enabled) && features.headerMenu.menu.widgetMap.hideColumn;
      if (otherHideableCols.length === 1) {
        const lastVisibleItem = siblingItems.find((menuItem) => menuItem.column === otherHideableCols[0]);
        if (lastVisibleItem) {
          lastVisibleItem.disabled = true;
        }
        if (hideItem && column.region === item.column.region) {
          hideItem.disabled = true;
        }
      } else {
        otherHideableCols.forEach((col) => {
          const siblingItem = siblingItems.find((sibling) => sibling.column === col);
          if (siblingItem) {
            siblingItem.disabled = false;
          }
        });
        if (hideItem && column.region === item.column.region) {
          hideItem.disabled = false;
        }
      }
      (_b2 = item.menu) == null ? void 0 : _b2.eachWidget((subItem) => {
        subItem.checked = checked;
      });
      const parentItem = menu.owner;
      if (parentItem && parentItem.column === column.parent) {
        parentItem.checked = siblingItems.some((subItem) => subItem.checked === true);
      }
    }
  }
  /**
   * Supply items to ColumnDragToolbar
   * @private
   */
  getColumnDragToolbarItems(column, items2) {
    const visibleInRegion = this.grid.columns.visibleColumns.filter((col) => col.region === column.region);
    if (column.hideable !== false && visibleInRegion.length > 1) {
      items2.push({
        text: "L{hideColumnShort}",
        ref: "hideColumn",
        group: "L{column}",
        localeClass: this,
        icon: "b-fw-icon b-icon-hide-column",
        weight: 101,
        onDrop: ({ column: column2 }) => column2.hide()
      });
    }
    return items2;
  }
  //endregion
};
//region Config
__publicField(ColumnPicker, "$name", "ColumnPicker");
__publicField(ColumnPicker, "configurable", {
  /**
   * Groups columns in the picker by region (each region gets its own sub menu)
   * @config {Boolean}
   * @default
   */
  groupByRegion: false,
  /**
   * Groups columns in the picker by tag, each column may be shown under multiple tags. See
   * {@link Grid.column.Column#config-tags}
   * @config {Boolean}
   * @default
   */
  groupByTag: false,
  /**
   * Configure this as `true` to have the fields from the Grid's {@link Core.data.Store}'s
   * {@link Core.data.Store#config-modelClass} added to the menu to create __new__ columns
   * to display the fields.
   *
   * This may be combined with the {@link Grid.view.mixin.GridState stateful} ability of the grid
   * to create a self-configuring grid.
   * @config {Boolean}
   * @default
   */
  createColumnsFromModel: false,
  menuCls: "b-column-picker-menu b-sub-menu"
});
ColumnPicker._$name = "ColumnPicker";
GridFeatureManager.registerFeature(ColumnPicker, true);

// ../Grid/lib/Grid/feature/ColumnRename.js
var ColumnRename = class extends InstancePlugin {
  doDestroy() {
    var _a5;
    (_a5 = this.editor) == null ? void 0 : _a5.destroy();
    super.doDestroy();
  }
  static get pluginConfig() {
    return {
      after: ["populateHeaderMenu"]
    };
  }
  populateHeaderMenu({ items: items2, column }) {
    items2.rename = {
      weight: 215,
      icon: "b-fw-icon b-icon-edit",
      text: this.L("L{rename}"),
      disabled: column.readOnly,
      onItem: () => this.startEdit(column)
    };
  }
  startEdit(column) {
    var _a5;
    if (column instanceof Event) {
      column = (_a5 = this.client.getHeaderDataFromEvent(column)) == null ? void 0 : _a5.column;
    }
    if (!column || column.readOnly || this.disabled) {
      return false;
    }
    const { textWrapper } = column;
    let { editor } = this;
    if (!editor) {
      this.editor = editor = new Editor({
        owner: this.client,
        align: {
          align: "t0-t0"
        }
      });
    }
    editor.render(textWrapper);
    editor.startEdit({
      target: textWrapper,
      record: column,
      field: "text"
    });
  }
};
__publicField(ColumnRename, "$name", "ColumnRename");
__publicField(ColumnRename, "configurable", {
  /**
   * See {@link #keyboard-shortcuts Keyboard shortcuts} for details
   * @config {Object<String,KeyMapConfig>}
   */
  keyMap: {
    F2: "startEdit"
  }
});
ColumnRename._$name = "ColumnRename";
GridFeatureManager.registerFeature(ColumnRename, false);

// ../Grid/lib/Grid/feature/ColumnReorder.js
var ColumnReorder = class extends Delayable_default(InstancePlugin) {
  constructor() {
    super(...arguments);
    __publicField(this, "ignoreSelectors", [
      ".b-grid-header-resize-handle",
      ".b-field"
    ]);
  }
  // Plugin configuration. This plugin chains some functions in Grid
  static get pluginConfig() {
    return {
      after: ["onInternalPaint", "renderContents"]
    };
  }
  /**
   * Initialize drag & drop (called from render)
   * @private
   */
  init() {
    const me = this, { grid } = me, gridEl = grid.element, containers = DomHelper.children(gridEl, ".b-grid-headers");
    containers.push(...DomHelper.children(gridEl, ".b-grid-header-children"));
    if (me.dragHelper) {
      me.dragHelper.containers = containers;
    } else {
      grid.whenVisible(() => me.createDragHelper());
    }
  }
  createDragHelper() {
    const me = this, { grid } = me, gridEl = grid.element, containers = DomHelper.children(gridEl, ".b-grid-headers");
    containers.push(...DomHelper.children(gridEl, ".b-grid-header-children"));
    me.dragHelper = new DragHelper({
      name: "columnReorder",
      mode: "container",
      dragThreshold: 10,
      targetSelector: ".b-grid-header",
      floatRootOwner: grid,
      rtlSource: grid,
      outerElement: grid.headerContainer,
      externalDropTargetSelector: ".b-groupbar, .b-columndragtoolbar",
      monitoringConfig: {
        scrollables: [{
          element: ".b-grid-headers"
        }]
      },
      scrollManager: ScrollManager.new({
        direction: "horizontal",
        element: grid.headerContainer
      }),
      containers,
      isElementDraggable(element) {
        const abort = Boolean(element.closest(me.ignoreSelectors.join(",")));
        if (abort || me.disabled) {
          return false;
        }
        const columnEl = element.closest(this.targetSelector), column = columnEl && grid.columns.getById(columnEl.dataset.columnId), isLast = (column == null ? void 0 : column.childLevel) === 0 && grid.subGrids[column.region].columns.count === 1;
        return Boolean(column) && column.draggable !== false && !isLast;
      },
      ignoreSelector: ".b-filter-icon,.b-grid-header-resize-handle",
      // Allow dragging columns over rows as well
      resolveTargetElement(element) {
        const cellElement = element.closest("[data-column-id]");
        if (cellElement) {
          return grid.columns.getById(cellElement.dataset.columnId).element;
        }
        return element;
      },
      internalListeners: {
        beforeDragStart: me.onBeforeDragStart,
        dragstart: me.onDragStart,
        drag: me.onDrag,
        drop: me.onDrop,
        abort: me.onAbort,
        thisObj: me
      }
    });
    me.relayEvents(me.dragHelper, ["dragStart", "drag", "drop", "abort"], "gridHeader");
    return me.dragHelper;
  }
  //endregion
  //region Plugin config
  doDestroy() {
    var _a5, _b2;
    (_a5 = this.dragHelper) == null ? void 0 : _a5.scrollManager.destroy();
    (_b2 = this.dragHelper) == null ? void 0 : _b2.destroy();
    super.doDestroy();
  }
  get grid() {
    return this.client;
  }
  //endregion
  //region Events (drop)
  onBeforeDragStart({ source: dragHelper, context, event }) {
    const me = this, { element } = context, column = context.column = me.client.columns.getById(element.dataset.columnId);
    dragHelper.autoSizeClonedTarget = !me.stretchedDragProxy && !me.usingGroupBarWidget;
    dragHelper.dropTargetSelector = me.stretchedDragProxy ? null : ".b-grid-headers, .b-groupbar, .b-columndragtoolbar";
    return column.allowDrag && me.client.trigger("beforeColumnDragStart", { column, event });
  }
  onDragStart({ context, event }) {
    const me = this, { grid, usingGroupBarWidget, dragHelper } = me, { column, dragProxy, element } = context, headerContainerBox = grid.element.querySelector(".b-grid-header-container").getBoundingClientRect();
    dragProxy.style.width = column.element.offsetWidth + "px";
    dragHelper.useCenteredCursor = !me.stretchedDragProxy;
    if (!grid.features.columnDragToolbar && !usingGroupBarWidget) {
      dragHelper.minY = headerContainerBox.top;
      if (me.stretchedDragProxy) {
        dragProxy.classList.add("b-column-reorder-stretched");
        DomHelper.setTranslateXY(dragProxy, element.left, element.top);
        dragHelper.maxY = grid.element.getBoundingClientRect().bottom;
      } else {
        dragHelper.maxY = headerContainerBox.bottom;
      }
      dragProxy.style.height = grid.bodyElement.offsetHeight + "px";
    } else {
      dragProxy.style.height = headerContainerBox.height + "px";
    }
    grid.element.classList.add("b-dragging-header");
    if (usingGroupBarWidget) {
      dragProxy.classList.add("b-grid-reordering-columns-with-groupbar");
    }
    if (grid.features.filterBar && grid.features.filterBar.compactMode) {
      dragProxy.classList.add("b-filter-bar-compact");
    }
    grid.trigger("columnDragStart", { column, event });
  }
  onDrag({ context, event }) {
    const me = this, grid = me.client, {
      column,
      insertBefore: insertBeforeElement
    } = context, insertBefore = grid.columns.getById(insertBeforeElement == null ? void 0 : insertBeforeElement.dataset.columnId), targetHeader = Widget.fromElement(event.target, "gridheader");
    if (targetHeader == null ? void 0 : targetHeader.subGrid.sealedColumns) {
      context.valid = false;
    }
    grid.trigger("columnDrag", { column, insertBefore, event, context });
  }
  /**
   * Handle drop
   * @private
   */
  onDrop({ context, event }) {
    var _a5;
    if (!context.valid) {
      return this.onInvalidDrop({ context, event });
    }
    const me = this, { grid } = me, { column } = context, element = context.dragging, onHeader = context.target.closest(".b-grid-header"), droppedInRegion = (_a5 = context.draggedTo) == null ? void 0 : _a5.dataset.region, isReorder = droppedInRegion || onHeader;
    let vetoed, newParent, insertBefore, toRegion, oldParent;
    grid.element.classList.remove("b-dragging-header");
    if (isReorder) {
      const onColumn = onHeader ? grid.columns.getById(onHeader.dataset.columnId) : grid.subGrids[droppedInRegion].columns.last, sibling = context.insertBefore;
      toRegion = droppedInRegion || onColumn.region;
      oldParent = column.parent;
      insertBefore = sibling ? grid.columns.getById(sibling.dataset.columnId) : grid.subGrids[toRegion].columns.last.nextSibling;
      if (insertBefore) {
        newParent = insertBefore.parent;
      } else {
        const groupNode = onHeader == null ? void 0 : onHeader.parentElement.closest(".b-grid-header");
        if (groupNode) {
          newParent = grid.columns.getById(groupNode.dataset.columnId);
        } else {
          newParent = grid.columns.rootNode;
        }
      }
      vetoed = toRegion === column.region && oldParent === newParent && (onColumn === column.previousSibling || insertBefore === column.nextSibling);
      element.remove();
    }
    vetoed = vetoed || grid.trigger("beforeColumnDropFinalize", {
      column,
      newParent,
      insertBefore,
      event,
      region: toRegion
    }) === false;
    if (!vetoed && isReorder) {
      const sorters = grid.columns.sorters.slice();
      grid.columns.sorters.length = 0;
      grid.eachSubGrid((subGrid) => {
        subGrid.columns.sorters.length = 0;
      });
      vetoed = !newParent.insertChild(column, insertBefore);
      if (vetoed && sorters.length) {
        grid.columns.sort(sorters);
      }
    }
    context.valid = !vetoed;
    if (!vetoed && isReorder) {
      if (column.parent.isRoot && column.region !== toRegion) {
        column.region = toRegion;
      }
      if (oldParent.children.length === 0) {
        oldParent.parent.removeChild(oldParent);
      }
    }
    grid.trigger("columnDrop", { column, newParent, insertBefore, valid: context.valid, event, region: toRegion });
  }
  onAbort({ context, event }) {
    this.onInvalidDrop({ context, event });
  }
  /**
   * Handle invalid drop
   * @private
   */
  onInvalidDrop({ context, event }) {
    const { grid } = this, { column } = context;
    grid.trigger("columnDrop", { column, valid: false, event });
    grid.element.classList.remove("b-dragging-header");
  }
  //endregion
  //region Render
  /**
   * Updates DragHelper with updated headers when grid contents is rerendered
   * @private
   */
  renderContents() {
    this.init();
  }
  /**
   * Initializes this feature on grid paint.
   * @private
   */
  onInternalPaint() {
    this.init();
  }
  /**
   * Returns true if a reorder operation is active
   * @property {Boolean}
   * @readonly
   */
  get isReordering() {
    var _a5;
    return Boolean((_a5 = this.dragHelper) == null ? void 0 : _a5.isDragging);
  }
  //endregion
};
//region Init
__publicField(ColumnReorder, "$name", "ColumnReorder");
__publicField(ColumnReorder, "configurable", {
  /**
   * Set to `true` to stretch the column drag proxy element to cover the full height of the grid.
   * @prp {Boolean}
   */
  stretchedDragProxy: null
});
ColumnReorder.featureClass = "b-column-reorder";
ColumnReorder._$name = "ColumnReorder";
GridFeatureManager.registerFeature(ColumnReorder, true);

// ../Grid/lib/Grid/feature/ColumnResize.js
var ColumnResize = class extends InstancePlugin {
  //region Init
  construct(grid, config) {
    const me = this;
    super.construct(grid, config);
    me.resizer = new ResizeHelper({
      name: "columnResize",
      targetSelector: ".b-grid-header",
      handleSelector: ".b-grid-header-resize-handle",
      outerElement: grid.element,
      rtlSource: grid,
      constrainToParent: false,
      internalListeners: {
        beforeresizestart: me.onBeforeResizeStart,
        resizestart: me.onResizeStart,
        resizing: me.onResizing,
        resize: me.onResize,
        cancel: me.onCancel,
        thisObj: me
      }
    });
  }
  doDestroy() {
    var _a5;
    (_a5 = this.resizer) == null ? void 0 : _a5.destroy();
    super.doDestroy();
  }
  //endregion
  changeLiveResize(liveResize) {
    if (liveResize === "auto") {
      return !BrowserHelper.isMobileSafari;
    }
    return liveResize;
  }
  //region Events
  onBeforeResizeStart({ event, element }) {
    const { client } = this;
    return !this.disabled && client.trigger("beforeColumnResize", {
      column: client.columns.getById(element.dataset.columnId),
      domEvent: event
    });
  }
  onResizeStart({ context }) {
    const { client, resizer } = this, column = context.column = client.columns.getById(context.element.dataset.columnId), { allRecords: siblings } = column.subGrid.columns;
    resizer.minWidth = column.minWidth;
    context.subGridBoundary = column.element.getBoundingClientRect()[client.rtl ? "left" : "right"];
    client.element.classList.add("b-column-resizing");
    siblings.slice(0, siblings.indexOf(column)).forEach((c) => {
      c.flex && c.element && c !== column.parent && c.set({
        width: c.element.offsetWidth,
        flex: ""
      });
    });
    client.trigger("columnResizeStart", {
      column,
      domEvent: context.event
    });
  }
  /**
   * Handle drag event - resize the column live unless it's a touch gesture
   * @private
   */
  onResizing({ context }) {
    if (context.valid && this.liveResize) {
      const { column } = context, { subGrid } = column, { scrollable, columns } = subGrid, { rtl } = this.client;
      this.client.resizingColumns = true;
      column.width = context.newWidth;
      subGrid.refreshFakeScroll();
      if (column === columns.last && (rtl ? context.currentX < context.subGridBoundary : context.currentX > context.subGridBoundary)) {
        scrollable.x = scrollable.maxX;
      }
    }
  }
  /**
   * Handle drop event (only used for touch)
   * @fires columnResize
   * @private
   */
  onResize({ context }) {
    const { client } = this, { column, event } = context;
    client.element.classList.remove("b-column-resizing");
    if (context.valid) {
      if (this.liveResize) {
        client.resizingColumns = false;
        client.afterColumnsResized(column);
      } else {
        column.width = context.newWidth;
      }
      client.trigger("columnResize", { column, domEvent: event });
    }
  }
  /**
   * Restore column width on cancel (ESC)
   * @private
   */
  onCancel({ context }) {
    const { client } = this;
    client.element.classList.remove("b-column-resizing");
    context.column.width = context.elementWidth;
    client.resizingColumns = false;
  }
  //endregion
};
__publicField(ColumnResize, "$name", "ColumnResize");
__publicField(ColumnResize, "configurable", {
  /**
   * Resize all cells below a resizing header during dragging.
   * `'auto'` means `true` on non-mobile platforms.
   * @config {'auto'|Boolean}
   * @default
   */
  liveResize: "auto"
});
ColumnResize._$name = "ColumnResize";
GridFeatureManager.registerFeature(ColumnResize, true);

// ../Grid/lib/Grid/feature/FillHandle.js
var FillHandle = class extends InstancePlugin.mixin(Delayable_default) {
  constructor() {
    super(...arguments);
    __publicField(this, "delayable", {
      handleSelection: "raf"
    });
  }
  // Plugin configuration. This plugin chains/overrides some functions in Grid.
  static get pluginConfig() {
    return {
      before: ["onStoreRemove"],
      chain: [
        "afterSelectionChange",
        "onContentChange",
        "fixElementHeights",
        "syncFlexedSubCols",
        "beforeSubGridInternalResize"
      ],
      override: ["getCellDataFromEvent"]
    };
  }
  afterConstruct() {
    super.afterConstruct();
    const { client } = this;
    if (!client.selectionMode.cell) {
      this.disabled = true;
    }
    client.ion({
      startCellEdit: "onStartCellEdit",
      finishCellEdit: "onFinishCellEdit",
      cancelCellEdit: "onFinishCellEdit",
      rowExpand: "onContentChange",
      rowCollapse: "onContentChange",
      thisObj: this
    });
    this._fillListeners = {};
  }
  async onContentChange() {
    this.removeElements();
    await this.client.waitForAnimations();
    this.handleSelection(true);
  }
  fixElementHeights() {
    this.handleSelection();
  }
  // This function is called if a subgrid changes width
  syncFlexedSubCols() {
    this.handleSelection();
  }
  // FillHandle elements needs to be removed while resize is calculating overflow
  beforeSubGridInternalResize() {
    this.removeElements();
  }
  onStoreRemove(event) {
    if (event.isCollapse) {
      this.handleSelection(true);
    }
  }
  onStartCellEdit() {
    this.removeElements(false);
  }
  onFinishCellEdit() {
    this.handleSelection();
  }
  getCellDataFromEvent(event, includeSingleAxisMatch) {
    if (includeSingleAxisMatch) {
      includeSingleAxisMatch = !event.target.classList.contains("b-fill-handle");
    }
    return this.overridden.getCellDataFromEvent(event, includeSingleAxisMatch);
  }
  get currentRangeForEvents() {
    if (this.currentRange) {
      let { from, to, negative } = this.currentRange;
      if (negative) {
        [from, to] = [to, from];
      }
      return { from, to };
    }
  }
  // region Pattern recognition
  findPatternsIn2dRange(range, horizontal, negative) {
    const values = {};
    for (const cell of range) {
      const id2 = horizontal ? cell.id : cell.columnId;
      let value2 = cell.record.getValue(cell.column.editorField);
      if (value2 && typeof value2 === "string" && !isNaN(value2)) {
        value2 = parseFloat(value2);
      }
      if (!values[id2]) {
        values[id2] = [];
      }
      values[id2].push(value2);
    }
    for (const rowOrCol in values) {
      values[rowOrCol].pattern = this.findPatternsIn1dRange(values[rowOrCol], negative);
    }
    return values;
  }
  findPatternsIn1dRange(range, negative) {
    const lastValue = range[negative ? 0 : range.length - 1], pattern = {
      next: () => lastValue,
      lastValue
    };
    if (range.every((val) => typeof val === "number") || range.every((val) => val instanceof Date)) {
      const diffs = range.map((val, ix) => val - range[ix - 1]);
      diffs.shift();
      if (new Set(diffs).size === 1) {
        pattern.increaseBy = diffs[0] * (negative ? -1 : 1);
        pattern.next = () => {
          if (pattern.lastValue instanceof Date) {
            pattern.lastValue = new Date(pattern.lastValue.getTime() + pattern.increaseBy);
          } else {
            pattern.lastValue += pattern.increaseBy;
          }
          return pattern.lastValue;
        };
      }
    } else if (range.length > 1) {
      pattern.stringPattern = [...range];
      pattern.next = () => {
        if (pattern.currentIndex === void 0) {
          pattern.currentIndex = 0;
        } else {
          pattern.currentIndex += 1;
          if (pattern.currentIndex >= pattern.stringPattern.length) {
            pattern.currentIndex = 0;
          }
        }
        return pattern.stringPattern[pattern.currentIndex];
      };
    }
    return pattern;
  }
  // endregion
  afterSelectionChange() {
    const me = this;
    if (me.client.readOnly || me.disabled) {
      me.removeElements();
      return;
    }
    if (GlobalEvents_default.isMouseDown()) {
      me.client.delayUntilMouseUp(() => me.handleSelection(true));
      me.removeElements();
    } else {
      me.handleSelection(true);
    }
  }
  /**
   * Checks selection and sees to it that fill handle and border is drawn.
   * Runs on next animation frame
   * @internal
   */
  handleSelection(clearCache = false) {
    if (this.enabled && !this._isExtending) {
      if (clearCache) {
        this._cachedSelectedRange = null;
      }
      const range = this.rangeSelection;
      if (range) {
        this.drawFillHandleAndBorder(range[0], range[range.length - 1]);
      } else {
        this.removeElements();
      }
    }
  }
  // region Mouse events
  // On fillHandle mouse down only
  onMouseDown(domEvent) {
    const { client } = this;
    if (this.enabled && !client.readOnly && client.trigger("beforeFillHandleDragStart", {
      cell: client.focusedCell,
      domEvent
    }) !== false) {
      this._fillListeners.mouseDownOver = EventHelper.on({
        element: this.rootElement,
        keydown: "onKeyDown",
        mouseenter: {
          handler: "onMouseOver",
          delegate: ".b-grid-cell",
          element: client.selectionDragMouseEventListenerElement
        },
        capture: true,
        thisObj: this
      });
      this._fillListeners.mouseUp = EventHelper.on({
        element: document,
        capture: true,
        mouseup: "onMouseUp",
        thisObj: this
      });
      domEvent.preventDefault();
      domEvent.stopImmediatePropagation();
      domEvent.handled = true;
      client.trigger("fillHandleDragStart", { cell: client.focusedCell, domEvent });
    }
  }
  // Responsible for doing the filling
  async onMouseUp(domEvent) {
    const result = await this.client.trigger("fillHandleBeforeDragFinalize", { ...this.currentRangeForEvents, domEvent }) !== false;
    this.finalize(result, domEvent);
  }
  finalize(commit, domEvent = true) {
    var _a5, _b2;
    const me = this, {
      client,
      currentRange,
      currentRangeForEvents,
      _isCropping
    } = me, range = me.rangeSelection, selectionChange = range && currentRange && client.internalSelectRange(currentRange.from, currentRange.to), selectedCells = (selectionChange == null ? void 0 : selectionChange.selectedCells) || [], extensionCells = _isCropping ? me.croppingCells : selectedCells.filter((cell) => !range.some((sel) => sel.equals(cell, true)));
    me._isCropping = null;
    if (me._isExtending) {
      client.disableScrollingCloseToEdges(client.items);
      me._isExtending = null;
    }
    if (!(extensionCells == null ? void 0 : extensionCells.length) || !commit) {
      me.currentRange = null;
      me.handleSelection();
      client.trigger("fillHandleDragAbort", { cell: client.focusedCell });
      return;
    }
    client.suspendRefresh();
    if (_isCropping) {
      extensionCells.forEach((cell) => cell.record.set(cell.column.field, null, false, false, false, true));
    } else {
      const [firstCell] = extensionCells, horizontal = range.some((sel) => sel.record === firstCell.record), negative = horizontal ? firstCell.columnIndex < range[0].columnIndex : firstCell.rowIndex < range[0].rowIndex, patterns = me.findPatternsIn2dRange(range, horizontal, negative), changeMap = /* @__PURE__ */ new Map();
      if (negative) {
        extensionCells.reverse();
      }
      for (const cell of extensionCells) {
        const { column, record } = cell;
        let value2 = (_a5 = me.calculateFillValue) == null ? void 0 : _a5.call(me, { range, column, record, cell });
        if (value2 === void 0) {
          const pattern = patterns[horizontal ? cell.id : cell.columnId].pattern;
          value2 = pattern.next();
        }
        if (column.canFillValue({ range, record, cell, value: value2, enforceType: horizontal })) {
          let changed = changeMap.get(record);
          if (!changed) {
            changed = {};
            changeMap.set(record, changed);
          }
          changed[column.field] = ((_b2 = column.calculateFillValue) == null ? void 0 : _b2.call(column, { value: value2, record, range, horizontal })) || value2;
        }
      }
      for (const [record, changes] of changeMap) {
        record.set(changes, null, null, null, null, true);
      }
    }
    client.resumeRefresh(true);
    client.performSelection(selectionChange);
    me.currentRange = null;
    me.handleSelection();
    client.trigger("fillHandleDragEnd", { ...currentRangeForEvents, domEvent });
  }
  // The fill border and handle should refresh on mouse move
  onMouseOver(domEvent) {
    var _a5;
    const me = this, {
      client,
      rangeSelection
    } = me, first = rangeSelection[0], last = rangeSelection[rangeSelection.length - 1], cellData = client.getCellDataFromEvent(domEvent, true);
    let cellSelector = cellData && client.normalizeCellContext(cellData.cellSelector);
    if (((_a5 = cellSelector == null ? void 0 : cellSelector._column) == null ? void 0 : _a5.region) === first._column.region) {
      const equalOrSmaller = rangeSelection.some((cs) => cs.equals(cellSelector, true));
      let negative;
      if (!me._isExtending) {
        client.enableScrollingCloseToEdges(client.items);
      }
      me._isCropping = equalOrSmaller && me.allowCropping && (cellSelector.rowIndex < last.rowIndex || cellSelector.columnIndex < last.columnIndex);
      if (!equalOrSmaller) {
        if (cellSelector.rowIndex >= first.rowIndex && cellSelector.rowIndex <= last.rowIndex) {
          negative = first.columnIndex > cellSelector.columnIndex;
          cellSelector = new GridLocation({
            grid: client,
            record: negative ? first.record : last.record,
            column: cellSelector.column
          });
        } else {
          negative = first.rowIndex > cellSelector.rowIndex;
          cellSelector = new GridLocation({
            grid: client,
            record: cellSelector.record,
            column: negative ? first.column : last.column
          });
        }
      }
      const from = negative ? cellSelector : first, to = negative || equalOrSmaller && !me._isCropping ? last : cellSelector;
      me.currentRange = { from, to, negative };
      me._isExtending = true;
      me.drawFillHandleAndBorder(from, to, true);
      client.trigger("fillHandleDrag", { ...me.currentRangeForEvents, domEvent });
    }
  }
  // endregion
  // region Creating, updating and removing fillhandle and fillborder
  drawFillHandleAndBorder(from, to, keepListeners = false) {
    var _a5, _b2;
    if (!from.cell && !to.cell) {
      return;
    }
    const me = this, {
      client,
      _fillListeners
    } = me, regionEl = client.subGrids[from.column.region].element, fromRect = Rectangle.from(from.cell || from.column.element, regionEl), toRect = Rectangle.from(to.cell || to.column.element, regionEl), y = (_a5 = client.getRecordCoords(from.record, true)) == null ? void 0 : _a5.y, bottom = toRect.bottom;
    let {
      borderElement,
      handleElement
    } = me;
    me.removeElements(keepListeners);
    if (!borderElement) {
      me.borderElement = borderElement = DomHelper.createElement({
        className: "b-fill-selection-border"
      });
      me.handleElement = handleElement = DomHelper.createElement({
        className: "b-fill-handle"
      });
    }
    if (client.rtl) {
      const regionRect = Rectangle.from(client.subGrids[from.column.region].element), x = regionRect.width - fromRect.right, width = toRect.left > fromRect.left ? toRect.right - fromRect.left : fromRect.right - toRect.left;
      DomHelper.setRect(borderElement, { x, y, width, height: bottom - y });
      DomHelper.setTopInsetInlineStart(handleElement, bottom, x + width);
      handleElement.classList.toggle("b-fill-handle-left-edge", toRect.left <= 0);
    } else {
      DomHelper.setRect(borderElement, { y, x: fromRect.x, width: toRect.right - fromRect.x, height: bottom - y });
      DomHelper.setTopInsetInlineStart(handleElement, bottom, toRect.right);
      handleElement.classList.toggle("b-fill-handle-right-edge", toRect.right >= regionEl.scrollWidth - 1);
    }
    regionEl.appendChild(borderElement);
    regionEl.appendChild(handleElement);
    me.toggleCroppingCls(false);
    me.croppingCells = null;
    if (me._isCropping && ((_b2 = me.rangeSelection) == null ? void 0 : _b2.length)) {
      const newCells = client.getRange(from, to);
      me.croppingCells = me.rangeSelection.filter((sel) => !newCells.some((cell) => cell.equals(sel, true)));
      me.toggleCroppingCls();
    }
    if (!_fillListeners.handleClick) {
      _fillListeners.handleClick = EventHelper.on({
        element: client.element,
        delegate: ".b-fill-handle",
        mousedown: "onMouseDown",
        thisObj: me
      });
    }
    me.hasFillElements = true;
  }
  toggleCroppingCls(add = true) {
    var _a5;
    (_a5 = this.croppingCells) == null ? void 0 : _a5.forEach((sel) => {
      var _a6;
      return (_a6 = this.client.getCell(sel)) == null ? void 0 : _a6.classList.toggle("b-indicate-crop", add);
    });
  }
  removeElements(keepListeners = false) {
    var _a5, _b2;
    const me = this;
    (_a5 = me.handleElement) == null ? void 0 : _a5.remove();
    (_b2 = me.borderElement) == null ? void 0 : _b2.remove();
    if (!keepListeners) {
      me.removeListeners();
    }
    me.hasFillElements = false;
  }
  // Detach listeners
  removeListeners() {
    const me = this;
    for (const listener in me._fillListeners) {
      me._fillListeners[listener]();
    }
    me._fillListeners = {};
  }
  // endregion
  // Gets current selection range. Only allows for single range or single cell.
  get rangeSelection() {
    if (!this._cachedSelectedRange) {
      this._cachedSelectedRange = this.currentSelectedRange;
    }
    return this._cachedSelectedRange;
  }
  // Calculate cell ranges
  get currentSelectedRange() {
    const { client } = this, { store } = client;
    let { selectedCells } = client;
    if (!client._selectedRows.length && selectedCells.length && selectedCells.every(({ record }) => store.indexOf(record) >= 0)) {
      if (selectedCells.length > 1) {
        selectedCells = selectedCells.sort((a, b) => a.rowIndex - b.rowIndex || a.columnIndex - b.columnIndex);
        const [first] = selectedCells, [last] = selectedCells.slice(-1), startRow = first.rowIndex, startCol = first.columnIndex, region = first.region, endRow = last.rowIndex, endCol = last.columnIndex, cellCount = (endCol - startCol + 1) * (endRow - startRow + 1);
        if (selectedCells.length === cellCount && selectedCells.every((cell) => cell.region === region && cell.rowIndex >= startRow && cell.rowIndex <= endRow && cell.columnIndex >= startCol && cell.columnIndex <= endCol)) {
          return selectedCells;
        }
      } else {
        return selectedCells;
      }
    }
  }
  onKeyDown(event) {
    if (event.key === "Escape") {
      this.abort();
    }
  }
  /**
   * Aborts an ongoing FillHandle drag operation
   */
  abort() {
    this.finalize(false);
    this.reset();
  }
  reset() {
    this._isExtending = this._isCropping = this.currentRange = this.croppingCells = null;
  }
  doDisable(disable) {
    this.removeElements();
    this.reset();
    super.doDisable(disable);
  }
};
__publicField(FillHandle, "$name", "FillHandle");
// region Events
/**
 * Fired before dragging of the FillHandle starts, return `false` to prevent the drag operation.
 * @preventable
 * @event beforeFillHandleDragStart
 * @param {Grid.util.GridLocation} cell Information about the column / record
 * @param {MouseEvent} domEvent The raw DOM event
 * @on-owner
 */
/**
 * Fired when dragging of the FillHandle starts.
 * @preventable
 * @event fillHandleDragStart
 * @param {Grid.util.GridLocation} cell Information about the column / record
 * @param {MouseEvent} domEvent The raw DOM event
 * @on-owner
 */
/**
 * Fired while dragging the FillHandle.
 * @event fillHandleDrag
 * @param {Grid.util.GridLocation} from The from cell
 * @param {Grid.util.GridLocation} to The to cell
 * @param {MouseEvent} domEvent The raw DOM event
 * @on-owner
 */
/**
 * Fired before the FillHandle dragging is finalized and values are applied to cells, return `false` to prevent the
 * drag operation from applying data changes.
 * @preventable
 * @async
 * @event fillHandleBeforeDragFinalize
 * @param {Grid.util.GridLocation} from The from cell
 * @param {Grid.util.GridLocation} to The to cell
 * @param {MouseEvent} domEvent The raw DOM event
 * @on-owner
 */
/**
 * Fired after a FillHandle drag operation.
 * @event fillHandleDragEnd
 * @param {Grid.util.GridLocation} from The from cell
 * @param {Grid.util.GridLocation} to The to cell
 * @param {MouseEvent} domEvent The raw DOM event
 * @on-owner
 */
/**
 * Fired when a FillHandle drag operation is aborted.
 * @event fillHandleDragAbort
 * @on-owner
 */
// endregion
__publicField(FillHandle, "configurable", {
  /**
   * Implement this function to be able to customize the value that cells will be filled with.
   * Return `undefined` to use default calculations.
   *
   * ```javascript
   * new Grid({
   *    features : {
   *        fillHandle : {
   *           calculateFillValue({cell, column, range, record}) {
   *              if(column.field === 'number') {
   *                 return range.reduce(
   *                    (sum, location) => sum + location.record[location.column.field]
   *                 );
   *              }
   *           }
   *        }
   *    }
   * });
   * ```
   *
   * @param {Object} data Object containing information about current cell and fill value
   * @param {Grid.util.GridLocation} data.cell Current cell data
   * @param {Grid.column.Column} data.column Current cell column
   * @param {Grid.util.GridLocation[]} data.range Range from where to calculate values
   * @param {Core.data.Model} data.record Current cell record
   * @returns {String|Number|Date} Value to fill current cell
   * @config {Function}
   */
  calculateFillValue: null,
  /**
   * Set to `true` to enable the fill range to crop the original selected range. This clears the cells which were
   * a part of the original selected range, but are no longer a part of the smaller range.
   * @config {Boolean}
   */
  allowCropping: false
});
FillHandle._$name = "FillHandle";
GridFeatureManager.registerFeature(FillHandle);

// ../Grid/lib/Grid/widget/GridFieldFilterPicker.js
var _GridFieldFilterPicker = class _GridFieldFilterPicker extends FieldFilterPicker {
  //endregion
  afterConstruct() {
    var _a5;
    const me = this;
    if (!me.grid) {
      throw new Error(`${me.constructor.$name} requires 'grid' to be configured.`);
    }
    me.fields = (_a5 = me.fields) != null ? _a5 : {};
    super.afterConstruct();
  }
  updateGrid(newGrid) {
    var _a5;
    if (!((_a5 = newGrid.store) == null ? void 0 : _a5.modelClass)) {
      throw new Error(`Grid does not have a store with a modelClass defined.`);
    }
    if (!newGrid.columns) {
      throw new Error(`Grid does not have a column store.`);
    }
  }
  /**
   * Gets the filterable fields backing any of the configured `grid`'s columns, for those columns for which
   * it is possible to do so.
   * @private
   * @returns {Object} Filterable fields dictionary of the form { [fieldName]: { title, type } }
   */
  static getColumnFields(columnStore, modelClass, allowedFieldNames) {
    var _a5;
    return Object.fromEntries(
      (_a5 = columnStore == null ? void 0 : columnStore.bottomColumns.reduce(
        (outFields, { field: fieldName, text, filterType, filterable, format: format2 }) => {
          var _a6;
          if (!allowedFieldNames || allowedFieldNames.includes(fieldName)) {
            const field = fieldName && modelClass.getFieldDefinition(fieldName), fieldType = isFilterableField(field) ? field.type : void 0;
            if (fieldType || filterType || filterable) {
              outFields.push([
                fieldName,
                {
                  title: text || fieldName,
                  type: (_a6 = filterType != null ? filterType : isSupportedDurationField(field) ? "duration" : fieldType) != null ? _a6 : "auto",
                  format: format2
                }
              ]);
            }
          }
          return outFields;
        },
        []
      )) != null ? _a5 : []
    );
  }
  changeFields(newFields) {
    var _a5;
    const mergedFields = ObjectHelper.merge(
      {},
      _GridFieldFilterPicker.getColumnFields(this.grid.columns, (_a5 = this.grid.store) == null ? void 0 : _a5.modelClass),
      newFields
    );
    return this.allowedFieldNames ? Object.fromEntries(this.allowedFieldNames.map((fieldName) => [fieldName, mergedFields[fieldName]])) : mergedFields;
  }
};
//region Config
__publicField(_GridFieldFilterPicker, "$name", "GridFieldFilterPicker");
// Factoryable type name
__publicField(_GridFieldFilterPicker, "type", "gridfieldfilterpicker");
/**
 * @hideconfigs store
 */
__publicField(_GridFieldFilterPicker, "configurable", {
  /**
   * {@link Grid.view.Grid} from which to read the available field list. In order to
   * appear as a selectable property for a filter, a column must have a `field` property.
   * If the column has a `text` property, that will be shown as the displayed text in the
   * selector; otherwise, the `field` property will be shown as-is.
   *
   * The grid's {@link Core.data.Store}'s {@link Core.data.Store#property-modelClass} will be
   * examined to find field data types.
   *
   * You can limit available fields to a subset of the grid's columns using the
   * {@link #config-allowedFieldNames} configuration property.
   *
   * @config {Grid.view.Grid}
   */
  grid: null,
  /**
   * Optional array of field names that are allowed as selectable properties for filters.
   * This is a subset of the field names found in the {@link #config-grid}'s columns. When supplied, only
   * the named fields will be shown in the property selector combo.
   *
   * Note that field names are case-sensitive and should match the data field name in the store
   * model.
   *
   * @config {String[]}
   */
  allowedFieldNames: null
});
var GridFieldFilterPicker = _GridFieldFilterPicker;
GridFieldFilterPicker.initClass();
GridFieldFilterPicker._$name = "GridFieldFilterPicker";

// ../Grid/lib/Grid/widget/GridFieldFilterPickerGroup.js
var GridFieldFilterPickerGroup = class extends FieldFilterPickerGroup {
  validateConfig() {
    if (!this.grid) {
      throw new Error(`${this.constructor.$name} requires the 'grid' config property.`);
    }
  }
  getFilterPickerConfig(filter2) {
    const { grid, allowedFieldNames, fields } = this;
    return {
      ...super.getFilterPickerConfig(filter2),
      fields,
      grid,
      allowedFieldNames
    };
  }
  get store() {
    return this.grid.$store;
  }
  // For backwards compat, always uses the grid's store so does nothing but preventing it from crash when configured
  // with a store
  set store(store) {
  }
};
//region Config
__publicField(GridFieldFilterPickerGroup, "$name", "GridFieldFilterPickerGroup");
// Factoryable type name
__publicField(GridFieldFilterPickerGroup, "type", "gridfieldfilterpickergroup");
/**
 * @hideconfigs fields, store
 */
__publicField(GridFieldFilterPickerGroup, "configurable", {
  /**
   * {@link Grid.view.Grid} from which to read the available field list. In order to
   * appear as a selectable property for a filter, a column must have a `field` property.
   * If the column has a `text` property, that will be shown as the displayed text in the
   * selector; otherwise, the `field` property will be shown as-is.
   *
   * The grid's {@link Core.data.Store}'s {@link Core.data.Store#property-modelClass} will be
   * examined to find field data types.
   *
   * You can limit available fields to a subset of the grid's columns using the
   * {@link #config-allowedFieldNames} configuration property.
   *
   * @config {Grid.view.Grid}
   */
  grid: null
});
//endregion
__publicField(GridFieldFilterPickerGroup, "childPickerType", "gridfieldfilterpicker");
GridFieldFilterPickerGroup.initClass();
GridFieldFilterPickerGroup._$name = "GridFieldFilterPickerGroup";

// ../Grid/lib/Grid/widget/ChecklistFilterCombo.js
var VALUE_COUNT = Symbol("valueCount");
var ChecklistFilterCombo = class extends Combo {
  changePicker(picker, oldPicker) {
    const merged = picker ? ObjectHelper.merge(
      {
        items: {
          list: {
            store: this.store,
            itemTpl: this.listItemTpl
          }
        }
      },
      picker,
      {
        selectedRecordCollection: this.valueCollection,
        cls: new DomClassList("b-checklistfiltercombo-picker", picker.cls)
      }
    ) : picker;
    return super.changePicker(merged, oldPicker);
  }
  updateReadOnly(readOnly) {
    super.updateReadOnly(...arguments);
    if (this._picker) {
      this.picker.readOnly = readOnly;
    }
  }
  updateStore(store) {
    const me = this;
    me.detachListeners("storeChange");
    if (store) {
      store.ion({
        name: "storeChange",
        change: me.updateListSelection,
        thisObj: me
      });
    }
    if (me.picker) {
      me.picker.widgetMap.list.store = store;
    }
    me.updateListSelection();
  }
  onListSelectionChange() {
    if (!this.showApplyButton) {
      this.applyListSelection();
    }
    this.picker.widgetMap.list.refresh();
    this.syncInputFieldValue();
  }
  applyListSelection() {
    const { selected } = this.picker.widgetMap.list;
    this.value = selected.map((m) => m[this.valueField]);
  }
  onSearchInput({ value: value2 }) {
    this.searchText = value2;
  }
  onSearchClear() {
    this.searchText = null;
  }
  updateSearchText(searchText) {
    const me = this;
    if ((searchText == null ? void 0 : searchText.trim().length) > 0) {
      me.store.filter({
        id: me.searchFilterId,
        property: me.displayField,
        operator: me.filterOperator,
        value: searchText,
        caseSensitive: false
      });
    } else {
      me.store.removeFilter(me.searchFilterId);
    }
    me.picker.widgetMap.searchField.value = searchText;
  }
  changeChipView() {
    return null;
  }
  onSelectAllClick() {
    this.searchText = null;
    this.picker.widgetMap.list.selectAll();
  }
  onRemoveAllClick() {
    this.clear();
    this.picker.widgetMap.list.deselectAll();
  }
  renderListItem(record) {
    var _a5;
    const pillHtml = (_a5 = this.listItemPillTpl) == null ? void 0 : _a5.call(this, record);
    return `<div class="b-checklistfiltercombo-item-text">${record.getValue(this.displayField)}</div>${pillHtml ? `<div class="b-checklistfiltercombo-item-pill">${pillHtml}</div>` : ""}`;
  }
  syncInputFieldValue(skipHighlight = true) {
    const me = this;
    if (me.picker) {
      const { values } = me.picker.widgetMap.list.selected;
      me.input.value = values.length > 0 ? me.L("L{filtersSet}", values.length, values) : "";
      if (!skipHighlight) {
        me.highlightChanged();
      }
    }
  }
  updateListSelection() {
    var _a5, _b2, _c2;
    (_c2 = this.picker) == null ? void 0 : _c2.widgetMap.list.select((_b2 = (_a5 = this.value) == null ? void 0 : _a5.map((id2) => this.store.find((record) => record[this.valueField] === id2, true))) != null ? _b2 : [], true);
  }
  set value(value2) {
    super.value = value2;
    this.updateListSelection();
  }
  get value() {
    return super.value;
  }
  updateShowApplyButton(showApplyButton) {
    this.picker.widgetMap.selectAllButton.hidden = showApplyButton;
    this.picker.widgetMap.applyButton.hidden = !showApplyButton;
  }
  onApplyClick() {
    this.applyListSelection();
  }
  showPicker() {
    super.showPicker(...arguments);
    this.picker.widgetMap.searchField.focus();
  }
};
__publicField(ChecklistFilterCombo, "$name", "ChecklistFilterCombo");
__publicField(ChecklistFilterCombo, "type", "checklistfiltercombo");
/**
 * @hideconfigs multiSelect
 */
__publicField(ChecklistFilterCombo, "configurable", {
  multiSelect: true,
  listItemTpl: "up.renderListItem",
  /**
   * Template to render optional content next to list items.
   * @prp {Function}
   * @param {Core.data.Model} record The list item's backing record
   * @returns {String}
   */
  listItemPillTpl: null,
  /**
   * When `true`, shows an "Apply" button in place of the "Select All" button, and does not
   * raise `change` events until the Apply button is clicked.
   * @prp {Boolean}
   * @default false
   */
  showApplyButton: false,
  picker: {
    type: "panel",
    flex: 1,
    tbar: {
      items: {
        searchField: {
          type: "textfield",
          placeholder: "L{ChecklistFilterCombo.search}",
          clearable: true,
          weight: 100,
          internalListeners: {
            input: "up.onSearchInput",
            clear: "up.onSearchClear"
          }
        }
      }
    },
    items: {
      list: {
        type: "list",
        multiSelect: true,
        emptyText: "L{ChecklistFilterCombo.noItems}",
        weight: 100,
        internalListeners: {
          selectionChange: "up.onListSelectionChange"
        }
      }
    },
    bbar: {
      items: {
        applyButton: {
          type: "button",
          text: "L{ChecklistFilterCombo.apply}",
          cls: "b-transparent",
          textAlign: "left",
          weight: 100,
          visible: false,
          internalListeners: {
            click: "up.onApplyClick"
          }
        },
        selectAllButton: {
          type: "button",
          text: "L{ChecklistFilterCombo.selectAll}",
          cls: "b-transparent",
          textAlign: "left",
          weight: 200,
          internalListeners: {
            click: "up.onSelectAllClick"
          }
        },
        removeAllButton: {
          type: "button",
          text: "L{ChecklistFilterCombo.removeAll}",
          cls: "b-transparent",
          textAlign: "left",
          weight: 300,
          internalListeners: {
            click: "up.onRemoveAllClick"
          }
        }
      }
    }
  },
  chipView: null,
  store: {
    data: []
  },
  editable: false,
  placeholder: "L{placeholder}",
  /**
   * The text in the search box, which will filter the list to matching records only. See also {@link #config-filterOperator}.
   * @prp {String}
   */
  searchText: null,
  searchFilterId: "search",
  /**
   * The initial value of this Combo box. An array of record ids.
   * @config {String[]|Number[]}
   */
  value: []
});
ChecklistFilterCombo.initClass();
ChecklistFilterCombo._$name = "ChecklistFilterCombo";

// ../Grid/lib/Grid/feature/Filter.js
var fieldTypeMap = {
  date: "date",
  int: "number",
  integer: "number",
  number: "number",
  text: "string",
  string: "string",
  duration: "duration",
  time: "time",
  auto: "auto"
};
var allowedFilterTypes = {
  ...filterableFieldDataTypes,
  relation: true
};
var menuItemsWithSeparators = {
  filterDateIsToday: true,
  filterDateIsThisWeek: true,
  filterDateIsThisMonth: true,
  filterDateIsThisYear: true
};
var Filter = class extends InstancePlugin {
  static get configurable() {
    return {
      /**
       * Use custom filtering functions defined on columns also when programmatically filtering by the columns
       * field.
       *
       * ```javascript
       * const grid = new Grid({
       *     columns : [
       *         {
       *             field : 'age',
       *             text : 'Age',
       *             filterable({ record, value }) {
       *               // Custom filtering, return true/false
       *             }
       *         }
       *     ],
       *
       *     features : {
       *         filter : {
       *             prioritizeColumns : true // <--
       *         }
       *     }
       * });
       *
       * // Because of the prioritizeColumns config above, any custom
       * // filterable function on a column will be used when
       * // programmatically filtering by that columns field
       * grid.store.filter({
       *     property : 'age',
       *     value    : 30
       * });
       * ```
       *
       * @config {Boolean}
       * @default
       * @category Common
       */
      prioritizeColumns: false,
      /**
       * See {@link #keyboard-shortcuts Keyboard shortcuts} for details
       * @config {Object<String,KeyMapConfig>}
       */
      keyMap: {
        f: "showFilterEditorByKey"
      },
      /**
       * Set to `true` to enable the more limited legacy UI mode.
       *
       * @config {Boolean}
       * @default
       * @category Common
       */
      legacyMode: false,
      /**
       * Optional configuration to use when configuring the {@link Grid.widget.GridFieldFilterPickerGroup} shown in the
       * column header popup, when not in legacy mode.
       *
       * @config {GridFieldFilterPickerGroupConfig}
       * @category Common
       */
      pickerConfig: null,
      /**
       * When true, close the popup when the last filter shown in the popup is removed using the remove button. Not
       * applicable in legacy mode.
       *
       * @config {Boolean}
       * @default
       * @category Common
       */
      closeEmptyPopup: false,
      /**
       * The value against which to compare the {@link #config-property} of candidate objects.
       * @config {*}
       */
      value: null,
      /**
       * The name of a property of candidate objects which yields the value to compare.
       * @config {String}
       */
      property: null,
      /**
       * The operator to use when comparing a candidate object's {@link #config-property}.
       * @config {CollectionCompareOperator}
       */
      operator: null,
      /**
       * Which operator to pre-fill for the blank filter shown by default in the filter pop-up, keyed by
       * the column field's data type. See {@link Core.util.CollectionFilter#typedef-CollectionCompareOperator}
       * for available operators.
       *
       * Default value:
       *
       * ```javascript
       * {
       *     date     : '=',
       *     number   : '=',
       *     string   : 'includes',
       *     duration : '=',
       *     relation : null,
       *     auto     : '*'
       * }
       * ```
       *
       * @config {Object}
       */
      defaultOperators: {
        date: "sameDay",
        number: "=",
        string: "includes",
        duration: "=",
        relation: null,
        auto: "*"
      },
      /**
       * This makes it possible to generally limit the allowed operators which populates the filtering sub-menus.
       * See {@link Core.util.CollectionFilter#typedef-CollectionCompareOperator} for available operators.
       *
       * ```javascript
       * const grid = new Grid({
       *     features : {
       *         filter : {
       *             allowedOperators : ['*', '=', '<', '>']
       *         }
       *     }
       * });
       * ```
       *
       * @config {Object}
       */
      allowedOperators: null,
      /**
       * Whether the feature is enabled on all columns by default. A column's `filterable.filter`
       * configuration overrides this setting.
       * @prp {Boolean}
       * @category Common
       * @default
       */
      defaultEnabled: true
    };
  }
  construct(client, config) {
    const me = this;
    me.closeFilterEditor = me.closeFilterEditor.bind(me);
    me.inferredTypes = {};
    super.construct(client, config);
    me.bindStore(me.store);
    if (config && typeof config === "object") {
      const clone = ObjectHelper.clone(config);
      delete clone.prioritizeColumns;
      delete clone.legacyMode;
      delete clone.pickerConfig;
      delete clone.dateFormat;
      delete clone.closeEmptyPopup;
      delete clone.defaultOperators;
      delete clone.defaultEnabled;
      delete clone.allowedOperators;
      if (!ObjectHelper.isEmpty(clone)) {
        me.store.filter(clone, null, client.isConfiguring);
      }
    }
  }
  doDestroy() {
    var _a5, _b2;
    (_a5 = this.filterTip) == null ? void 0 : _a5.destroy();
    (_b2 = this.filterEditorPopup) == null ? void 0 : _b2.destroy();
    super.doDestroy();
  }
  get store() {
    return this.client.$store;
  }
  updateAllowedOperators(allowedOperators) {
    const asterix = allowedOperators == null ? void 0 : allowedOperators.indexOf("*");
    if (asterix >= 0) {
      allowedOperators[asterix] = "includes";
    }
  }
  bindStore() {
    this.detachListeners("store");
    this.store.ion({
      name: "store",
      beforeFilter: "onStoreBeforeFilter",
      filter: "onStoreFilter",
      thisObj: this
    });
    if (this.client.isPainted) {
      this.refreshHeaders(false);
    }
  }
  //endregion
  //region Plugin config
  // Plugin configuration. This plugin chains some of the functions in Grid.
  static get pluginConfig() {
    return {
      chain: ["renderHeader", "populateCellMenu", "populateHeaderMenu", "onElementClick", "bindStore"]
    };
  }
  //endregion
  //region Refresh headers
  /**
   * Update headers to match stores filters. Called on store load and grid header render.
   * @param reRenderRows Also refresh rows?
   * @private
   */
  refreshHeaders(reRenderRows) {
    const me = this, { client, store } = me, element = client.headerContainer;
    if (element) {
      DomHelper.children(element, ".b-filter-icon.b-latest").forEach((iconElement) => iconElement.classList.remove("b-latest"));
      if (!me.filterTip) {
        me.filterTip = new Tooltip({
          forElement: element,
          forSelector: ".b-filter-icon",
          getHtml({ activeTarget }) {
            return activeTarget.dataset.filterText;
          }
        });
      }
      if (!store.isFiltered) {
        me.filterTip.hide();
      }
      client.columns.visibleColumns.forEach((column) => {
        if (me.canFilterColumn(column)) {
          const columnFilters = store.filters.allValues.filter(({ property, disabled, internal, isNoOp }) => property === column.field && !disabled && !internal && !isNoOp), isColumnFiltered = columnFilters.length > 0, headerEl = column.element;
          if (headerEl) {
            const textEl = column.textWrapper;
            let filterIconEl = textEl == null ? void 0 : textEl.querySelector(".b-filter-icon"), filterText;
            if (isColumnFiltered) {
              const bullet = "&#x2022 ";
              filterText = `${me.L("L{filter}")}: ` + (columnFilters.length > 1 ? "<br/><br/>" : "") + columnFilters.map((columnFilter) => {
                var _a5, _b2, _c2, _d;
                let value2 = (_a5 = columnFilter.value) != null ? _a5 : "";
                const isArray4 = Array.isArray(value2), relation = (_b2 = store == null ? void 0 : store.modelRelations) == null ? void 0 : _b2.find(
                  ({ foreignKey }) => foreignKey === columnFilter.property
                );
                if (columnFilter.displayValue) {
                  value2 = columnFilter.displayValue;
                } else {
                  if (!me.legacyMode && relation) {
                    const { relatedDisplayField } = (_d = (_c2 = me.pickerConfig) == null ? void 0 : _c2.fields) == null ? void 0 : _d[columnFilter.property];
                    if (relatedDisplayField) {
                      const getDisplayValue = (foreignId) => {
                        var _a6;
                        return (_a6 = relation.foreignStore.getById(foreignId)) == null ? void 0 : _a6[relatedDisplayField];
                      };
                      if (isArray4) {
                        value2 = value2.map(getDisplayValue).sort((a, b) => (a != null ? a : "").localeCompare(b != null ? b : ""));
                      } else {
                        value2 = getDisplayValue(value2);
                      }
                    }
                  } else if (column.formatValue && value2) {
                    value2 = isArray4 ? value2.map((val) => column.formatValue(val)) : column.formatValue(value2);
                  }
                  if (isArray4) {
                    value2 = `[ ${value2.join(", ")} ]`;
                  }
                }
                const localizedOperator = FieldFilterPicker.localizeOperator(
                  columnFilter.operator,
                  me.getFilterType(column)
                );
                return (columnFilters.length > 1 ? bullet : "") + (typeof columnFilter === "string" ? columnFilter : `${localizedOperator} ${value2}`);
              }).join("<br/><br/>");
            } else {
              filterText = me.L("L{applyFilter}");
            }
            if (!filterIconEl) {
              filterIconEl = DomHelper.createElement({
                parent: textEl,
                tag: "i",
                className: "b-icon b-filter-icon",
                dataset: {
                  filterText
                }
              });
            } else {
              filterIconEl.dataset.filterText = filterText;
            }
            if (column.field === store.latestFilterField)
              filterIconEl.classList.add("b-latest");
            headerEl.classList.add("b-filterable");
            headerEl.classList.toggle("b-filter", isColumnFiltered);
          }
          column.meta.isFiltered = isColumnFiltered;
        }
      });
      if (reRenderRows) {
        client.refreshRows();
      }
    }
  }
  //endregion
  //region Filter
  applyFilter(column, filterConfig) {
    const { store } = this;
    column.$filter = store.addFilter(this.injectColumnFilterConfig(column, filterConfig), true);
    store.filter();
  }
  injectColumnFilterConfig(column, filterConfig) {
    const { filterFn } = column.filterable;
    return ObjectHelper.assign(filterConfig, {
      ...column.filterable,
      ...filterConfig,
      property: column.field,
      // Only inject a filterBy configuration if the column has a custom filterBy
      [filterFn ? "filterBy" : "_"]: function(record) {
        return filterFn({ value: this.value, record, operator: this.operator, property: this.property, column });
      }
    });
  }
  removeFilter(column, onlyForOperator) {
    var _a5;
    if (!this.legacyMode && !((_a5 = column.filterable) == null ? void 0 : _a5.filterField)) {
      for (const filter2 of this.getCurrentMultiFilters(column)) {
        if (!onlyForOperator || filter2.operator === onlyForOperator) {
          this.store.removeFilter(filter2);
        }
      }
    } else {
      this.store.removeFilter(this.store.filters.getBy("property", column.field));
    }
  }
  disableFilter(column) {
    for (const filter2 of this.getCurrentMultiFilters(column)) {
      filter2.disabled = true;
      this.store.filter(filter2);
    }
    this.store.filter();
  }
  getCurrentMultiFilters(column) {
    return this.store.filters.values.filter((filter2) => filter2.property === column.field);
  }
  getPopupDateItems(column, fieldType, filter2, initialValue, store, changeCallback, closeCallback, filterField) {
    const me = this, onClose = changeCallback;
    function onClear() {
      me.removeFilter(column);
    }
    function onKeydown({ event }) {
      if (event.key === "Enter") {
        changeCallback();
      }
    }
    function onChange({ source, value: value2 }) {
      if (value2 == null) {
        onClear();
      } else {
        me.clearSiblingsFields(source);
        me.applyFilter(column, { operator: source.operator, value: value2, displayValue: source._value, type: "date" });
      }
    }
    return [
      ObjectHelper.assign({
        type: "date",
        ref: "on",
        placeholder: "L{on}",
        localeClass: me,
        clearable: true,
        label: '<i class="b-fw-icon b-icon-filter-equal"></i>',
        value: (filter2 == null ? void 0 : filter2.operator) === "sameDay" ? filter2.value : initialValue,
        operator: "sameDay",
        onKeydown,
        onChange,
        onClose,
        onClear
      }, filterField),
      ObjectHelper.assign({
        type: "date",
        ref: "before",
        placeholder: "L{before}",
        localeClass: me,
        clearable: true,
        label: '<i class="b-fw-icon b-icon-filter-before"></i>',
        value: (filter2 == null ? void 0 : filter2.operator) === "<" ? filter2.value : null,
        operator: "<",
        onKeydown,
        onChange,
        onClose,
        onClear
      }, filterField),
      ObjectHelper.assign({
        type: "date",
        ref: "after",
        cls: "b-last-row",
        placeholder: "L{after}",
        localeClass: me,
        clearable: true,
        label: '<i class="b-fw-icon b-icon-filter-after"></i>',
        value: (filter2 == null ? void 0 : filter2.operator) === ">" ? filter2.value : null,
        operator: ">",
        onKeydown,
        onChange,
        onClose,
        onClear
      }, filterField)
    ];
  }
  getPopupNumberItems(column, fieldType, filter2, initialValue, store, changeCallback, closeCallback, filterField) {
    const me = this, onEsc = changeCallback;
    function onClear() {
      me.removeFilter(column);
    }
    function onKeydown({ event }) {
      if (event.key === "Enter") {
        changeCallback();
      }
    }
    function onChange({ source, value: value2 }) {
      if (value2 == null) {
        onClear();
      } else {
        me.clearSiblingsFields(source);
        me.applyFilter(column, { operator: source.operator, value: value2 });
      }
    }
    return [
      ObjectHelper.assign({
        type: "number",
        placeholder: "L{Filter.equals}",
        localeClass: me,
        clearable: true,
        label: '<i class="b-fw-icon b-icon-filter-equal"></i>',
        value: (filter2 == null ? void 0 : filter2.operator) === "=" ? filter2.value : initialValue,
        operator: "=",
        onKeydown,
        onChange,
        onEsc,
        onClear
      }, filterField),
      ObjectHelper.assign({
        type: "number",
        placeholder: "L{lessThan}",
        localeClass: me,
        clearable: true,
        label: '<i class="b-fw-icon b-icon-filter-less"></i>',
        value: (filter2 == null ? void 0 : filter2.operator) === "<" ? filter2.value : null,
        operator: "<",
        onKeydown,
        onChange,
        onEsc,
        onClear
      }, filterField),
      ObjectHelper.assign({
        type: "number",
        cls: "b-last-row",
        placeholder: "L{moreThan}",
        localeClass: me,
        clearable: true,
        label: '<i class="b-fw-icon b-icon-filter-more"></i>',
        value: (filter2 == null ? void 0 : filter2.operator) === ">" ? filter2.value : null,
        operator: ">",
        onKeydown,
        onChange,
        onEsc,
        onClear
      }, filterField)
    ];
  }
  clearSiblingsFields(sourceField) {
    var _a5;
    (_a5 = this.filterEditorPopup) == null ? void 0 : _a5.items.forEach((field) => {
      field !== sourceField && (field == null ? void 0 : field.clear());
    });
  }
  getPopupDurationItems(column, fieldType, filter2, initialValue, store, changeCallback, closeCallback, filterField) {
    const me = this, onEsc = changeCallback, onClear = () => me.removeFilter(column);
    function onChange({ source, value: value2 }) {
      if (value2 == null) {
        onClear();
      } else {
        me.clearSiblingsFields(source);
        me.applyFilter(column, { operator: source.operator, value: value2 });
      }
    }
    return [
      ObjectHelper.assign({
        type: "duration",
        placeholder: "L{Filter.equals}",
        localeClass: me,
        clearable: true,
        label: '<i class="b-fw-icon b-icon-filter-equal"></i>',
        value: (filter2 == null ? void 0 : filter2.operator) === "=" ? filter2.value : initialValue,
        operator: "=",
        onChange,
        onEsc,
        onClear
      }, filterField),
      ObjectHelper.assign({
        type: "duration",
        placeholder: "L{lessThan}",
        localeClass: me,
        clearable: true,
        label: '<i class="b-fw-icon b-icon-filter-less"></i>',
        value: (filter2 == null ? void 0 : filter2.operator) === "<" ? filter2.value : null,
        operator: "<",
        onChange,
        onEsc,
        onClear
      }, filterField),
      ObjectHelper.assign({
        type: "duration",
        cls: "b-last-row",
        placeholder: "L{moreThan}",
        localeClass: me,
        clearable: true,
        label: '<i class="b-fw-icon b-icon-filter-more"></i>',
        value: (filter2 == null ? void 0 : filter2.operator) === ">" ? filter2.value : null,
        operator: ">",
        onChange,
        onEsc,
        onClear
      }, filterField)
    ];
  }
  getPopupStringItems(column, fieldType, filter2, initialValue, store, changeCallback, closeCallback, filterField) {
    var _a5;
    const me = this;
    return [ObjectHelper.assign({
      type: "text",
      cls: "b-last-row",
      placeholder: "L{filter}",
      localeClass: me,
      clearable: true,
      label: '<i class="b-fw-icon b-icon-filter-equal"></i>',
      value: filter2 ? (_a5 = filter2.value) != null ? _a5 : filter2 : initialValue,
      operator: "*",
      onChange({ source, value: value2 }) {
        if (value2 === "") {
          closeCallback();
        } else {
          me.applyFilter(column, { operator: source.operator, value: value2, displayValue: source.displayField && source.records ? source.records.map((rec) => rec[source.displayField]).join(", ") : void 0 });
          if (!source.multiSelect) {
            changeCallback();
          }
        }
      },
      onClose: changeCallback,
      onClear: closeCallback
    }, filterField)];
  }
  /**
   * Get fields to display in filter popup.
   * @param {Grid.column.Column} column Column
   * @param fieldType Type of field, number, date etc.
   * @param filter Current filter
   * @param initialValue
   * @param store Grid store
   * @param changeCallback Callback for when filter has changed
   * @param closeCallback Callback for when editor should be closed
   * @param filterField filter field
   * @returns {*}
   * @private
   */
  getPopupItems(column, fieldType, filter2, initialValue, store, changeCallback, closeCallback, filterField) {
    const me = this;
    if (me.useLegacyModeForColumn(column) || filterField) {
      switch (fieldType) {
        case "date":
          return me.getPopupDateItems(...arguments);
        case "number":
          return me.getPopupNumberItems(...arguments);
        case "duration":
          return me.getPopupDurationItems(...arguments);
        default:
          return me.getPopupStringItems(...arguments);
      }
    }
    return [me.getFieldFilterPickerGroup(column)];
  }
  getAllowedOperators(column) {
    var _a5;
    let { allowedOperators } = this;
    if ((_a5 = column == null ? void 0 : column.filterable) == null ? void 0 : _a5.allowedOperators) {
      allowedOperators = allowedOperators != null ? allowedOperators : [];
      allowedOperators.push(...column.filterable.allowedOperators);
    }
    return allowedOperators;
  }
  getFieldFilterPickerGroup(column) {
    var _a5;
    const me = this, filterType = me.getFilterType(column), allowedOperators = me.getAllowedOperators(column), pickerGroupConfig = {
      fields: {
        [column.field]: {
          type: filterType,
          title: (_a5 = column.text) != null ? _a5 : column.field
        }
      },
      ...me.pickerConfig,
      type: "gridfieldfilterpickergroup",
      ref: "pickerGroup",
      limitToProperty: column.field,
      grid: me.client,
      propertyFieldCls: "b-transparent property-field",
      operatorFieldCls: "b-transparent operator-field",
      valueFieldCls: "b-transparent value-field",
      internalListeners: {
        beforeAddFilter: ({ filter: filter2 }) => {
          me.injectColumnFilterConfig(column, filter2);
        },
        remove: me.onPopupFilterRemove,
        keydown: me.onPopupKeydown,
        thisObj: me
      },
      triggerChangeOnInput: false
    };
    if (allowedOperators) {
      pickerGroupConfig.operators = {
        [filterType]: FieldFilterPicker.defaultOperators[filterType].filter(({ value: value2 }) => allowedOperators.includes(value2))
      };
    }
    return pickerGroupConfig;
  }
  onPopupFilterRemove() {
    if (this.closeEmptyPopup && this.filterEditorPopup.widgetMap.pickerGroup.filters.length === 0) {
      this.delay(this.closeFilterEditor, 0);
    }
  }
  onPopupKeydown({ event }) {
    if (event.key === "Enter") {
      this.delay(this.closeFilterEditor, 0);
    }
  }
  /**
   * Shows a popup where a filter can be edited.
   * @param {Grid.column.Column|String} column Column to show filter editor for
   * @param {*} [value] The initial value of the filter value input
   * @param {String} [operator] The initial operator of the filter operator selector (non-legacy mode)
   * @param {Boolean} [forceAddBlank] Whether to add a blank filter row even if other filters exist
   *                  (non-legacy mode; default false)
   */
  showFilterEditor(column, value2, operator, forceAddBlank = false) {
    var _a5, _b2;
    column = this.client.columns.getById(column);
    const me = this, { store } = me, headerEl = column.element, filter2 = store.filters.getBy("property", column.field), fieldType = me.getFilterType(column), legacyMode = me.useLegacyModeForColumn(column), { filterable } = column;
    if (!me.canFilterColumn(column)) {
      return;
    }
    me.closeFilterEditor();
    const items2 = me.getPopupItems(
      column,
      fieldType,
      // Only pass filter if it's not an internal filter
      (filter2 == null ? void 0 : filter2.internal) ? null : filter2,
      value2,
      store,
      me.closeFilterEditor,
      () => {
        me.removeFilter(column);
        me.closeFilterEditor();
      },
      filterable.filterField
    );
    items2.forEach((item) => item.placeholder = item.placeholder ? this.L(item.placeholder) : item.placeholder);
    me.filterEditorPopup = new Popup({
      forElement: headerEl,
      owner: me.client,
      cls: new DomClassList("b-filter-popup", {
        "b-filter-popup-legacymode": legacyMode
      }),
      scrollAction: "realign",
      layout: {
        type: "vbox",
        align: "stretch"
      },
      items: items2,
      dataset: {
        column: column.field,
        fieldType
      }
    });
    if (!legacyMode) {
      if (forceAddBlank || !(store == null ? void 0 : store.filters.find((filter3) => filter3.property === column.field))) {
        const allowedOperators = me.getAllowedOperators(column), operatorsByType = (_a5 = me.filterEditorPopup.widgetMap.pickerGroup.operators) != null ? _a5 : FieldFilterPicker.configurable.operators, availableOperators = operatorsByType[fieldType];
        let defaultOperator = me.defaultOperators[fieldType];
        if (!availableOperators.find(({ value: value3 }) => value3 === defaultOperator)) {
          defaultOperator = (_b2 = availableOperators[0]) == null ? void 0 : _b2.value;
        }
        if (allowedOperators && !allowedOperators.includes(defaultOperator)) {
          defaultOperator = allowedOperators[0];
        }
        me.filterEditorPopup.widgetMap.pickerGroup.addFilter({
          type: fieldType,
          property: column.field,
          operator: operator != null ? operator : defaultOperator,
          value: value2
        });
      }
      me.filterEditorPopup.items[0].focus();
    }
    const [firstItem] = me.filterEditorPopup.items;
    if (filterable.filterField && firstItem.isCombo && filterable.getFilterableValues) {
      const filterableValues = ArrayHelper.countUnique(store.allRecords.flatMap(filterable.getFilterableValues));
      firstItem.store.data = filterableValues.map(
        ({ value: value3, count }) => ({
          id: value3,
          [column.field]: value3,
          [VALUE_COUNT]: count
        })
      );
      firstItem.showPicker();
    }
  }
  /**
   * Close the filter editor.
   */
  closeFilterEditor() {
    var _a5;
    (_a5 = this.filterEditorPopup) == null ? void 0 : _a5.setTimeout(this.filterEditorPopup.destroy);
    this.filterEditorPopup = null;
  }
  //endregion
  //region Context menu
  getFilterType(column) {
    var _a5, _b2, _c2, _d;
    const me = this, { store } = me, fieldName = column.field, field = fieldName && store.modelClass.getFieldDefinition(fieldName), type = (_b2 = (_a5 = column.filterType) != null ? _a5 : fieldTypeMap[column.type]) != null ? _b2 : fieldTypeMap[field == null ? void 0 : field.type], relation = (_c2 = store == null ? void 0 : store.modelRelations) == null ? void 0 : _c2.find(({ foreignKey }) => foreignKey === fieldName);
    if (relation) {
      return "relation";
    } else if (type === "auto" && store && !me.useLegacyModeForColumn(column)) {
      if (store.totalCount > 0) {
        me.inferredTypes[fieldName] = FieldFilterPicker.inferFieldType(store, fieldName);
      }
      return (_d = me.inferredTypes[fieldName]) != null ? _d : "auto";
    } else if (type) {
      return fieldTypeMap[type];
    }
    return "auto";
  }
  populateCellMenuWithDateItems({ column, record, items: items2 }) {
    const property = column.field, type = this.getFilterType(column);
    if (type === "date") {
      const me = this, value2 = record.getValue(property), filter2 = (operator) => {
        me.applyFilter(column, {
          operator,
          value: value2,
          displayValue: column.formatValue ? column.formatValue(value2) : value2,
          type: "date"
        });
      };
      items2.filterDateEquals = {
        text: "L{on}",
        localeClass: me,
        icon: "b-fw-icon b-icon-filter-equal",
        cls: "b-separator",
        weight: 300,
        disabled: me.disabled,
        onItem: () => filter2("=")
      };
      items2.filterDateBefore = {
        text: "L{before}",
        localeClass: me,
        icon: "b-fw-icon b-icon-filter-before",
        weight: 310,
        disabled: me.disabled,
        onItem: () => filter2("<")
      };
      items2.filterDateAfter = {
        text: "L{after}",
        localeClass: me,
        icon: "b-fw-icon b-icon-filter-after",
        weight: 320,
        disabled: me.disabled,
        onItem: () => filter2(">")
      };
    }
  }
  populateCellMenuWithNumberItems({ column, record, items: items2 }) {
    const property = column.field, type = this.getFilterType(column);
    if (type === "number") {
      const me = this, value2 = record.getValue(property), filter2 = (operator) => {
        me.applyFilter(column, { operator, value: value2 });
      };
      items2.filterNumberEquals = {
        text: "L{equals}",
        localeClass: me,
        icon: "b-fw-icon b-icon-filter-equal",
        cls: "b-separator",
        weight: 300,
        disabled: me.disabled,
        onItem: () => filter2("=")
      };
      items2.filterNumberLess = {
        text: "L{lessThan}",
        localeClass: me,
        icon: "b-fw-icon b-icon-filter-less",
        weight: 310,
        disabled: me.disabled,
        onItem: () => filter2("<")
      };
      items2.filterNumberMore = {
        text: "L{moreThan}",
        localeClass: me,
        icon: "b-fw-icon b-icon-filter-more",
        weight: 320,
        disabled: me.disabled,
        onItem: () => filter2(">")
      };
    }
  }
  populateCellMenuWithDurationItems({ column, record, items: items2 }) {
    const type = this.getFilterType(column);
    if (type === "duration") {
      const me = this, value2 = column.getFilterableValue(record), filter2 = (operator) => {
        me.applyFilter(column, { operator, value: value2 });
      };
      items2.filterDurationEquals = {
        text: "L{equals}",
        localeClass: me,
        icon: "b-fw-icon b-icon-filter-equal",
        cls: "b-separator",
        weight: 300,
        disabled: me.disabled,
        onItem: () => filter2("=")
      };
      items2.filterDurationLess = {
        text: "L{lessThan}",
        localeClass: me,
        icon: "b-fw-icon b-icon-filter-less",
        weight: 310,
        disabled: me.disabled,
        onItem: () => filter2("<")
      };
      items2.filterDurationMore = {
        text: "L{moreThan}",
        localeClass: me,
        icon: "b-fw-icon b-icon-filter-more",
        weight: 320,
        disabled: me.disabled,
        onItem: () => filter2(">")
      };
    }
  }
  populateCellMenuWithStringItems({ column, record, items: items2 }) {
    var _a5, _b2;
    const type = this.getFilterType(column);
    if (!/(date|number|duration)/.test(type)) {
      const me = this, value2 = column.getFilterableValue(record), operator = (_b2 = (_a5 = column.filterable.filterField) == null ? void 0 : _a5.operator) != null ? _b2 : "*";
      items2.filterStringEquals = {
        text: "L{equals}",
        localeClass: me,
        icon: "b-fw-icon b-icon-filter-equal",
        cls: "b-separator",
        weight: 300,
        disabled: me.disabled,
        onItem: () => me.applyFilter(column, { value: value2, operator })
      };
    }
  }
  /**
   * In non-legacy mode, gets the cell/header context menu items: a top-level Filter item having a submenu with
   * operator and edit/remove options). Not used by legacy mode.
   * @private
   */
  getMenuItems(column, record) {
    const me = this, submenuItems = {}, type = me.getFilterType(column);
    if (column.filterable === false) {
      return {};
    }
    if (allowedFilterTypes[type]) {
      const value2 = record ? column.getFilterableValue(record) : void 0, activeOperators = me.getCurrentMultiFilters(column).filter((filter2) => !filter2.disabled).map(({ operator }) => operator), maxArgCount = record ? 1 : void 0, columnAllowedOperators = me.getAllowedOperators(column), allowedOperators = FieldFilterPicker.defaultOperators[type].filter(({ value: value3, argCount, isArrayValued }) => !(argCount > maxArgCount || isArrayValued) && (!columnAllowedOperators || columnAllowedOperators.includes(value3)));
      let weight = 300;
      for (const { value: operator, text, argCount } of allowedOperators) {
        const key = `filter${StringHelper.capitalize(type)}${StringHelper.capitalize(operator)}`;
        submenuItems[key] = {
          text: StringHelper.capitalize(FieldFilterPicker.L(text)),
          weight: weight += 10,
          icon: activeOperators.includes(operator) ? "b-icon b-icon-check" : null,
          disabled: me.disabled,
          cls: menuItemsWithSeparators[key] ? "b-separator" : null,
          onItem: () => me.onOperatorMenuItem(column, value2, operator, argCount)
        };
      }
    }
    if (column.meta.isFiltered) {
      Object.assign(submenuItems, me.getMenuItemsForFilteredColumn(column, record !== void 0));
    }
    return {
      filterMenu: {
        text: "L{filter}",
        localeClass: me,
        menu: submenuItems,
        icon: "b-fw-icon b-icon-filter",
        weight: record ? 400 : 100
      }
    };
  }
  /**
   * Handle clicking on an operator item in the filter submenu.
   * @param {Grid.column.Column} column The column to which the menu belongs
   * @param {*} value The cell value if this context menu belongs to a grid cell, undefined if header menu
   * @param {String} operator The selected operator, e.g. `'='`, `'isToday'`. See `CollectionFilter`.
   * @param {String} type The selected operator, e.g. `'='`, `'isToday'`. See `CollectionFilter`.
   * @param {Number} argCount The number of arguments required by the operator
   * @private
   */
  onOperatorMenuItem(column, value2, operator, argCount = 1) {
    const me = this, type = me.getFilterType(column), wasActive = me.getCurrentMultiFilters(column).find((filter2) => !filter2.disabled && filter2.operator === operator);
    if (wasActive) {
      me.removeFilter(column, operator);
    } else {
      if (argCount == 0 || value2 !== void 0) {
        me.applyFilter(column, {
          property: column.field,
          operator,
          type,
          value: argCount === 1 ? value2 : null,
          caseSensitive: false,
          disabled: value2 == null && argCount > 0
          // Can't apply filter yet; incomplete
        });
      } else {
        me.showFilterEditor(column, null, operator, true);
      }
    }
  }
  /**
   * Get the context menu items (cell and header) that apply when the column is already filtered, e.g. edit,
   * remove, disable. Used by both legacy and regular modes.
   * @param {Grid.column.Column} column The column to which the menu pertains
   * @param {Boolean} isCellMenu Whether this is a cell's context menu (not header)
   * @returns {Object<String,MenuItemConfig>} An `items` config containing the appropriate menu item configs
   * @private
   */
  getMenuItemsForFilteredColumn(column, isCellMenu) {
    const me = this, canRemoveFilter = !me.disabled && (me.legacyMode || me.columnHasRemovableFilters(column));
    return {
      // Don't show 'edit' in legacy mode cell menu (legacy mode header menu handled elsewhere)
      filterEdit: me.legacyMode ? void 0 : {
        text: "L{editFilter}",
        localeClass: me,
        icon: "b-fw-icon b-icon-edit",
        cls: "b-separator",
        weight: 500,
        disabled: !canRemoveFilter,
        onItem: () => me.showFilterEditor(column)
      },
      filterRemove: {
        text: "L{removeFilter}",
        localeClass: me,
        icon: "b-fw-icon b-icon-remove",
        cls: "b-separator",
        weight: 510,
        disabled: !canRemoveFilter,
        onItem: () => me.removeFilter(column)
      },
      filterDisable: {
        text: "L{disableFilter}",
        localeClass: me,
        icon: "b-fw-icon b-icon-filter-disable",
        weight: 520,
        disabled: me.disabled || !me.columnHasEnabledFilters(column),
        onItem: () => me.disableFilter(column)
      }
    };
  }
  /**
   * Add menu items for filtering.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Core.data.Model} options.record Record for which the menu will be shown
   * @param {Object<String,MenuItemConfig|Boolean|null>} options.items A named object to describe menu items
   * @internal
   */
  populateCellMenu({ column, record, items: items2, ...rest }) {
    const me = this;
    if (me.canFilterColumn(column) && !record.isSpecialRow) {
      if (me.useLegacyModeForColumn(column)) {
        me.populateCellMenuWithDateItems({ column, record, items: items2, ...rest });
        me.populateCellMenuWithNumberItems({ column, record, items: items2, ...rest });
        me.populateCellMenuWithDurationItems({ column, record, items: items2, ...rest });
        me.populateCellMenuWithStringItems({ column, record, items: items2, ...rest });
        if (column.meta.isFiltered) {
          Object.assign(items2, me.getMenuItemsForFilteredColumn(column, true));
        }
      } else {
        Object.assign(items2, me.getMenuItems(column, record));
      }
    }
  }
  /**
   * Used to determine whether the 'remove filters' menu item should be enabled.
   * @internal
   */
  columnHasRemovableFilters(column) {
    const me = this;
    return Boolean(me.getCurrentMultiFilters(column).find((filter2) => !me.canDeleteFilter || me.callback(me.canDeleteFilter, me, [filter2]) !== false));
  }
  /**
   * Used to determine whether the 'disable filters' menu item should be enabled.
   * @internal
   */
  columnHasEnabledFilters(column) {
    return Boolean(this.getCurrentMultiFilters(column).find((filter2) => !filter2.disabled));
  }
  /**
   * Add menu item for removing filter if column is filtered.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Object<String,MenuItemConfig|Boolean|null>} options.items A named object to describe menu items
   * @internal
   */
  populateHeaderMenu({ column, items: items2 }) {
    const me = this;
    if (me.useLegacyModeForColumn(column)) {
      if (column.meta.isFiltered) {
        items2.filterEdit = {
          text: "L{editFilter}",
          localeClass: me,
          weight: 100,
          icon: "b-fw-icon b-icon-filter",
          cls: "b-separator",
          disabled: me.disabled,
          onItem: () => me.showFilterEditor(column)
        };
        items2.filterRemove = {
          text: "L{removeFilter}",
          localeClass: me,
          weight: 110,
          icon: "b-fw-icon b-icon-remove",
          disabled: me.disabled || !me.legacyMode && !me.columnHasRemovableFilters(column),
          onItem: () => me.removeFilter(column)
        };
        items2.filterDisable = {
          text: "L{disableFilter}",
          localeClass: me,
          icon: "b-fw-icon b-icon-filter-disable",
          weight: 115,
          disabled: me.disabled || !me.columnHasEnabledFilters(column),
          onItem: () => me.disableFilter(column)
        };
      } else if (me.canFilterColumn(column)) {
        items2.filter = {
          text: "L{filter}",
          localeClass: me,
          weight: 100,
          icon: "b-fw-icon b-icon-filter",
          cls: "b-separator",
          disabled: me.disabled,
          onItem: () => me.showFilterEditor(column)
        };
      }
    } else {
      Object.assign(items2, me.getMenuItems(column));
    }
  }
  useLegacyModeForColumn(column) {
    var _a5;
    return this.legacyMode || ((_a5 = column.filterable) == null ? void 0 : _a5.filterField) != void 0;
  }
  canFilterColumn(column) {
    var _a5, _b2;
    return column.filterable !== false && (((_a5 = column.filterable) == null ? void 0 : _a5.filter) === true || ((_b2 = column.filterable) == null ? void 0 : _b2.filter) !== false && this.defaultEnabled !== false);
  }
  //endregion
  //region Events
  // Intercept filtering by a column that has a custom filtering fn, and inject that fn
  onStoreBeforeFilter({ filters }) {
    const { columns } = this.client, filtersByColumn = ArrayHelper.groupBy(filters, "property");
    for (const [property, columnFilters] of Object.entries(filtersByColumn)) {
      const column = columns.find((col) => this.canFilterColumn(col) && col.field === property);
      if (column) {
        column.$filters = columnFilters.map((filter2, index) => {
          var _a5, _b2;
          if ((filter2.columnOwned || this.prioritizeColumns) && !filter2.internal && ((_a5 = column == null ? void 0 : column.filterable) == null ? void 0 : _a5.filterFn)) {
            const $filter = new CollectionFilter({
              id: (_b2 = filter2.id) != null ? _b2 : `${property}-${index}-${Date.now()}`,
              columnOwned: true,
              property,
              operator: filter2.operator,
              value: filter2.value,
              displayValue: filter2.displayValue,
              disabled: filter2.disabled,
              filterBy(record) {
                return column.filterable.filterFn.call(this, {
                  value: this.value,
                  record,
                  operator: this.operator,
                  property: this.property,
                  column
                });
              }
            });
            filters.splice(filters.indexOf(filter2), 1, $filter);
            return $filter;
          }
          return filter2;
        });
      }
    }
  }
  /**
   * Store filtered; refresh headers.
   * @private
   */
  onStoreFilter() {
    this.refreshHeaders(false);
  }
  /**
   * Called after headers are rendered, make headers match stores initial sorters
   * @private
   */
  renderHeader() {
    this.refreshHeaders(false);
  }
  /**
   * Called when user clicks on the grid. Only care about clicks on the filter icon.
   * @param {MouseEvent} event
   * @private
   */
  onElementClick({ target }) {
    if (this.filterEditorPopup) {
      this.closeFilterEditor();
    }
    if (target.classList.contains("b-filter-icon")) {
      const headerEl = target.closest(".b-grid-header");
      this.showFilterEditor(headerEl.dataset.columnId);
      return false;
    }
  }
  /**
   * Called when user presses F-key grid.
   * @param {MouseEvent} event
   * @private
   */
  showFilterEditorByKey({ target }) {
    const headerEl = target.matches(".b-grid-header") && target;
    if (headerEl) {
      this.showFilterEditor(headerEl.dataset.columnId);
    }
    return Boolean(headerEl);
  }
  // Only care about F key when a filterable header is focused
  isActionAvailable({ event, actionName }) {
    if (actionName === "showFilterEditorByKey") {
      const headerElement = event.target.closest(".b-grid-header"), column = headerElement && this.client.columns.find((col) => col.id === headerElement.dataset.columnId);
      return Boolean(column == null ? void 0 : column.filterable);
    }
  }
  //endregion
};
//region Init
__publicField(Filter, "$name", "Filter");
Filter._$name = "Filter";
GridFeatureManager.registerFeature(Filter);

// ../Grid/lib/Grid/feature/FilterBar.js
var complexOperators = {
  "*": null,
  isIncludedIn: null,
  startsWith: null,
  endsWidth: null
};
var FilterBar = class extends InstancePlugin {
  static get configurable() {
    return {
      /**
       * Use custom filtering functions defined on columns also when programmatically filtering by the columns
       * field.
       *
       * ```javascript
       * const grid = new Grid({
       *     columns : [
       *         {
       *             field : 'age',
       *             text : 'Age',
       *             filterable({ record, value }) {
       *               // Custom filtering, return true/false
       *             }
       *         }
       *     ],
       *
       *     features : {
       *         filterBar : {
       *             prioritizeColumns : true // <--
       *         }
       *     }
       * });
       *
       * // Because of the prioritizeColumns config above, any custom
       * // filterable function on a column will be used when
       * // programmatically filtering by that columns field
       * grid.store.filter({
       *     property : 'age',
       *     value    : 30
       * });
       * ```
       *
       * @config {Boolean}
       * @default
       * @category Common
       */
      prioritizeColumns: false,
      /**
       * The delay in milliseconds to wait after the last keystroke before applying filters.
       * Set to 0 to not trigger filtering from keystrokes, requires pressing ENTER instead
       * @config {Number}
       * @default
       * @category Common
       */
      keyStrokeFilterDelay: 300,
      /**
       * Toggle compact mode. In this mode the filtering fields are styled to transparently overlay the headers,
       * occupying no additional space.
       * @member {Boolean} compactMode
       * @category Common
       */
      /**
       * Specify `true` to enable compact mode for the filter bar. In this mode the filtering fields are styled
       * to transparently overlay the headers, occupying no additional space.
       * @config {Boolean}
       * @default
       * @category Common
       */
      compactMode: false,
      /**
       * By default, column filter is removed when a column is hidden or this feature is disabled. Set this flag
       * to `false` to keep filters in these scenarios.
       * @member {Boolean}
       * @category Common
       * @default
       */
      clearStoreFiltersOnHide: true,
      /**
       * Determines `filterBar` visibility. By default it is set to `false` and to hide the `filterBar` set
       * this flag to `true`.
       * @prp {Boolean}
       * @category Common
       * @default
       */
      hidden: false,
      /**
       * Use to set initial filter.
       *
       * ```javascript
       * const grid = new Grid({
       *   features : {
       *     filterBar : { filter: { property : 'city', value : 'Gavle' } }
       *   }
       * });
       * ```
       *
       * @config {CollectionFilterConfig|Function}
       * @returns {Boolean}
       * @category Common
       */
      filter: null,
      keyMap: {
        // Private
        ArrowUp: { handler: "disableGridNavigation", preventDefault: false },
        ArrowRight: { handler: "disableGridNavigation", preventDefault: false },
        ArrowDown: { handler: "disableGridNavigation", preventDefault: false },
        ArrowLeft: { handler: "disableGridNavigation", preventDefault: false },
        Enter: { handler: "disableGridNavigation", preventDefault: false }
      },
      /**
       * Whether the feature is enabled on all columns by default. A column's `filterable.filterBar`
       * configuration overrides this setting.
       * @prp {Boolean}
       * @category Common
       * @default
       */
      defaultEnabled: true
    };
  }
  static get pluginConfig() {
    return {
      before: ["renderContents"],
      chain: ["afterColumnsChange", "renderHeader", "populateHeaderMenu", "bindStore"]
    };
  }
  static get properties() {
    return {
      filterFieldCls: "b-filter-bar-field",
      filterFieldInputCls: "b-filter-bar-field-input",
      filterableColumnCls: "b-filter-bar-enabled",
      filterFieldInputSelector: ".b-filter-bar-field-input",
      filterableColumnSelector: ".b-filter-bar-enabled",
      filterParseRegExp: /^\s*([<>=*])?(.*)$/,
      storeTrackingSuspended: 0
    };
  }
  //endregion
  //region Init
  construct(client, config) {
    const me = this;
    me.onColumnFilterFieldChange = me.onColumnFilterFieldChange.bind(me);
    super.construct(client, Array.isArray(config) ? {
      filter: config
    } : config);
    me.bindStore(me.store);
    if (me.filter) {
      me.store.filter(me.filter);
    }
    me.gridDetacher = client.ion({ beforeElementClick: "onBeforeElementClick", thisObj: me });
  }
  get store() {
    return this.client.$store;
  }
  bindStore(store) {
    this.detachListeners("store");
    store.ion({
      name: "store",
      beforeFilter: "onStoreBeforeFilter",
      filter: "onStoreFilter",
      thisObj: this
    });
  }
  doDestroy() {
    var _a5;
    this.destroyFilterBar();
    (_a5 = this.gridDetacher) == null ? void 0 : _a5.call(this);
    super.doDestroy();
  }
  doDisable(disable) {
    const { columns } = this.client;
    columns == null ? void 0 : columns.forEach((column) => {
      const widget = this.getColumnFilterField(column);
      if (widget) {
        widget.disabled = disable;
      }
    });
    super.doDisable(disable);
  }
  updateHidden(hidden) {
    if (!this.isConfiguring) {
      if (hidden) {
        this.hideFilterBar();
      } else {
        this.showFilterBar();
      }
    }
  }
  updateCompactMode(value2) {
    this.client.headerContainer.classList[value2 ? "add" : "remove"]("b-filter-bar-compact");
    for (const prop in this._columnFilters) {
      const field = this._columnFilters[prop];
      field.placeholder = value2 ? field.placeholder || field.column.headerText : null;
    }
  }
  //endregion
  //region FilterBar
  destroyFilterBar() {
    var _a5;
    (_a5 = this.client.columns) == null ? void 0 : _a5.forEach(this.destroyColumnFilterField, this);
  }
  /**
   * Hides the filtering fields.
   */
  hideFilterBar() {
    var _a5;
    const me = this;
    me.clearStoreFiltersOnHide && me.suspendStoreTracking();
    (_a5 = me.client.columns) == null ? void 0 : _a5.forEach((col) => me.hideColumnFilterField(col, true));
    me.store.filter();
    me.clearStoreFiltersOnHide && me.resumeStoreTracking();
    me._hidden = true;
  }
  /**
   * Shows the filtering fields.
   */
  showFilterBar() {
    this.suspendStoreTracking();
    this.renderFilterBar(this.clearStoreFiltersOnHide);
    this.resumeStoreTracking();
    this._hidden = false;
  }
  /**
   * Toggles the filtering fields visibility.
   */
  toggleFilterBar() {
    if (this.hidden) {
      this.showFilterBar();
    } else {
      this.hideFilterBar();
    }
  }
  /**
   * Renders the filtering fields for filterable columns.
   * @private
   */
  renderFilterBar(applyFilter) {
    if (this.client.hideHeaders) {
      return;
    }
    this.client.columns.visibleColumns.forEach((column) => this.renderColumnFilterField(column, applyFilter));
    this.rendered = true;
  }
  //endregion
  //region FilterBar fields
  /**
   * Renders text field filter in the provided column header.
   * @param {Grid.column.Column} column Column to render text field filter for.
   * @private
   */
  renderColumnFilterField(column, applyFilters) {
    var _a5, _b2, _c2;
    const me = this, { client, store } = me, filterable = me.getColumnFilterable(column);
    if (filterable && column.isVisible) {
      const { field } = column, headerEl = column.element, filter2 = store.filters.get(column.id) || store.filters.getBy("property", field);
      let widget = me.getColumnFilterField(column);
      if (!widget) {
        const type = `${column.filterType || "text"}field`, { filterField } = filterable, externalCls = filterField == null ? void 0 : filterField.cls;
        if (externalCls) {
          delete filterable.filterField.cls;
        }
        const ariaLabel = (_a5 = filterField == null ? void 0 : filterField.ariaLabel) != null ? _a5 : column.text ? me.L(`L{Filter.filter} ${column.text}`) : null;
        widget = Widget.append(ObjectHelper.assign({
          type,
          cls: {
            [me.filterFieldCls]: 1,
            [externalCls]: externalCls
          },
          // Simplifies debugging / testing
          dataset: {
            column: field
          },
          column,
          owner: client,
          clearable: true,
          revertOnEscape: true,
          name: field,
          value: filter2 && !filter2._filterBy && !filter2.internal ? me.buildFilterValue(filter2) : void 0,
          inputCls: me.filterFieldInputCls,
          keyStrokeChangeDelay: me.keyStrokeFilterDelay,
          disabled: me.disabled,
          placeholder: me.compactMode ? column.headerText : null,
          // Also copy formats, DateColumn, TimeColumn etc
          format: column.format,
          ariaLabel,
          internalListeners: {
            change: me.onColumnFilterFieldChange,
            clear: me.onColumnFilterFieldChange
          }
        }, filterField), headerEl)[0];
        if (me.compactMode) {
          widget.placeholder = (filterField == null ? void 0 : filterField.placeholder) || column.headerText;
        }
        if (!(filterField == null ? void 0 : filterField.hasOwnProperty("min"))) {
          Object.defineProperty(widget, "min", {
            get: () => {
              var _a6;
              return (_a6 = column.editor) == null ? void 0 : _a6.min;
            },
            set: () => null
          });
        }
        if (!(filterField == null ? void 0 : filterField.hasOwnProperty("max"))) {
          Object.defineProperty(widget, "max", {
            get: () => {
              var _a6;
              return (_a6 = column.editor) == null ? void 0 : _a6.max;
            },
            set: () => null
          });
        }
        if (!(filterField == null ? void 0 : filterField.hasOwnProperty("strictParsing"))) {
          Object.defineProperty(widget, "strictParsing", {
            get: () => {
              var _a6;
              return (_a6 = column.editor) == null ? void 0 : _a6.strictParsing;
            },
            set: () => null
          });
        }
        widget.element.retainElement = true;
        me.setColumnFilterField(column, widget);
        const hasFilterFieldStoreData = (filterField == null ? void 0 : filterField.store) && (filterField.store.readUrl || filterField.store.data || filterField.store.isChained) || (filterField == null ? void 0 : filterField.items);
        if (widget.isCombo && !hasFilterFieldStoreData && widget.store.count === 0) {
          const configuredValue = widget.value, refreshData = () => {
            if (!(widget.store.readUrl || widget.store.isChained)) {
              let values;
              if (filterable.getFilterableValues) {
                const filterableValues = ArrayHelper.countUnique(store.flatMap(filterable.getFilterableValues));
                values = filterableValues.map(({ value: value2, count }) => ({
                  id: value2,
                  [field]: value2,
                  [VALUE_COUNT]: count
                }));
              } else {
                values = store.getDistinctValues(field, true, true).map(
                  ({ value: value2, count }) => store.createRecord({
                    id: value2,
                    [field]: value2,
                    [VALUE_COUNT]: count
                  })
                );
              }
              widget.store.data = values;
            }
          };
          widget.value = null;
          if (!widget.store.isSorted) {
            widget.store.sort({
              field,
              ascending: true
            });
          }
          widget.picker.ion({ beforeShow: refreshData });
          refreshData();
          widget.value = configuredValue;
          widget.displayField = (_b2 = filterField.displayField) != null ? _b2 : field;
          widget.valueField = (_c2 = filterField.valueField) != null ? _c2 : field;
        }
        if (!me.filter && widget.value && store.autoLoad !== false) {
          me.onColumnFilterFieldChange({ source: widget, value: widget.value, silent: true });
        }
      } else {
        if (applyFilters) {
          me.onColumnFilterFieldChange({ source: widget, value: widget.value });
        }
        widget.render(headerEl);
        widget.show();
      }
      headerEl.classList.add(me.filterableColumnCls);
    }
  }
  /**
   * Fills in column filter fields with values from the grid store filters.
   * @private
   */
  updateColumnFilterFields() {
    const me = this, { store } = me, { columns } = me.client;
    let field, filter2;
    me._updatingFields = true;
    for (const column of columns.visibleColumns) {
      field = me.getColumnFilterField(column);
      if (field && !column.$isApplyingFilter) {
        filter2 = store.filters.get(column.id) || store.filters.getBy("property", column.field);
        if (filter2 && !filter2.internal) {
          if (!filter2._filterBy) {
            field.value = me.buildFilterValue(filter2);
          } else {
            field.value = filter2.value;
          }
        } else if (field.isCombo && field.multiSelect) {
          field.value = [];
        } else {
          field.value = "";
        }
      }
    }
    me._updatingFields = false;
  }
  getColumnFilterable(column) {
    if (!column.isRoot && this.canFilterColumn(column) && column.field && column.isLeaf) {
      if (typeof column.filterable === "function") {
        column.filterable = {
          filterFn: column.filterable
        };
      }
      return column.filterable;
    }
  }
  destroyColumnFilterField(column) {
    const widget = this.getColumnFilterField(column);
    if (widget) {
      this.hideColumnFilterField(column, true);
      widget.destroy();
      this.setColumnFilterField(column, void 0);
    }
  }
  hideColumnFilterField(column, silent) {
    const me = this, { store } = me, columnEl = column.element, widget = me.getColumnFilterField(column);
    if (widget) {
      if (!me.isDestroying) {
        widget.hide();
      }
      const { $filter } = column;
      if (!store.isDestroyed && me.clearStoreFiltersOnHide && $filter) {
        store.removeFilter($filter, silent);
      }
      columnEl == null ? void 0 : columnEl.classList.remove(me.filterableColumnCls);
    }
  }
  /**
   * Returns column filter field instance.
   * @param {Grid.column.Column} column Column to get filter field for.
   * @returns {Core.widget.Widget}
   */
  getColumnFilterField(column) {
    var _a5;
    return (_a5 = this._columnFilters) == null ? void 0 : _a5[column.id];
  }
  setColumnFilterField(column, widget) {
    this._columnFilters = this._columnFilters || {};
    this._columnFilters[column.data.id] = widget;
  }
  //endregion
  //region Filters
  parseFilterValue(column, value2, field) {
    var _a5;
    if (Array.isArray(value2) || value2 instanceof Duration || typeof value2 === "number") {
      return {
        value: value2
      };
    }
    if (ObjectHelper.isDate(value2)) {
      return {
        operator: field.isDateField ? "sameDay" : field.isTimeField ? "sameTime" : "=",
        value: value2
      };
    }
    const match = String(value2).match(this.filterParseRegExp), defaultOperator = typeof value2 === "number" ? "=" : "*";
    return {
      operator: match[1] || ((_a5 = column.filterable) == null ? void 0 : _a5.operator) || defaultOperator,
      value: match[2]
    };
  }
  buildFilterValue({ operator, value: value2 }) {
    return typeof value2 === "number" || value2 instanceof Date || Array.isArray(value2) || value2 instanceof Duration ? value2 : (operator in complexOperators ? "" : operator) + value2;
  }
  //endregion
  // region Events
  // Intercept filtering by a column that has a custom filtering fn, and inject that fn
  onStoreBeforeFilter({ filters }) {
    var _a5;
    const { columns } = this.client;
    for (let i = 0; i < filters.count; i++) {
      const filter2 = filters.getAt(i), column = (filter2.columnOwned || this.prioritizeColumns) && columns.find((col) => this.canFilterColumn(col) && col.field === filter2.property);
      if ((_a5 = column == null ? void 0 : column.filterable) == null ? void 0 : _a5.filterFn) {
        if (!column.$filter) {
          column.$filter = new CollectionFilter({
            columnOwned: true,
            property: filter2.property,
            id: column.id,
            filterBy(record) {
              return column.filterable.filterFn({
                value: this.value,
                record,
                property: this.property,
                column
              });
            }
          });
        }
        column.$filter.value = filter2.value;
        filters.splice(i, 1, column.$filter);
      }
    }
  }
  /**
   * Fires when store gets filtered. Refreshes field values in column headers.
   * @private
   */
  onStoreFilter() {
    if (!this.storeTrackingSuspended && this.rendered) {
      this.updateColumnFilterFields();
    }
  }
  afterColumnsChange({ action, changes, column, columns }) {
    var _a5, _b2, _c2;
    const me = this;
    for (const col of me.client.columns.visibleColumns) {
      if (me.canFilterColumn(col) && (((_a5 = col.filterable) == null ? void 0 : _a5.filter) === true || ((_b2 = me.client.features.filter) == null ? void 0 : _b2.defaultEnabled) && ((_c2 = col.filterable) == null ? void 0 : _c2.filter) !== false)) {
        throw new Error(`Columns do not currently support both \`filter\` and \`filterBar\` features enabled on the same column. Please check column with id ${col.id}.`);
      }
    }
    if (!me.hidden && (changes == null ? void 0 : changes.hidden)) {
      const hidden = changes.hidden.value;
      if (hidden) {
        me.destroyColumnFilterField(column);
      } else {
        me.renderColumnFilterField(column);
      }
    }
    if (action === "remove") {
      columns.forEach((col) => me.destroyColumnFilterField(col));
    }
  }
  suspendStoreTracking() {
    this.storeTrackingSuspended++;
  }
  resumeStoreTracking() {
    this.storeTrackingSuspended--;
  }
  /**
   * Called after headers are rendered, make headers match stores initial sorters
   * @private
   */
  renderHeader() {
    if (!this.hidden) {
      this.renderFilterBar();
    }
  }
  renderContents() {
    if (this._columnFilters) {
      for (const field of Object.values(this._columnFilters)) {
        field == null ? void 0 : field.element.remove();
      }
    }
  }
  disableGridNavigation(event) {
    return event.target.matches(this.filterFieldInputSelector);
  }
  onBeforeElementClick({ event }) {
    if (event.target.closest(`.${this.filterFieldCls}`)) {
      return false;
    }
  }
  /**
   * Called when a column text filter field value is changed by user.
   * @param  {Core.widget.TextField} field Filter text field.
   * @param  {String} value New filtering value.
   * @private
   */
  onColumnFilterFieldChange({ source: field, value: value2, silent }) {
    var _a5, _b2, _c2, _d, _e;
    const me = this, { column } = field, { filterFn } = column.filterable, { store } = me, filter2 = column.$filter || store.filters.find((f) => (f.id === column.id || f.property === column.field) && !f.internal);
    if (me._updatingFields) {
      return;
    }
    const isClearingFilter = value2 == null || value2 === "" || Array.isArray(value2) && value2.length === 0;
    store.removeFilter(filter2, true);
    column.$filter = null;
    column.$isApplyingFilter = true;
    if (isClearingFilter) {
      if (!filter2) {
        column.$isApplyingFilter = false;
        return;
      }
    } else {
      column.$filter = store.addFilter({
        property: field.name,
        ...me.parseFilterValue(column, value2, field),
        [typeof ((_a5 = column.filterable) == null ? void 0 : _a5.caseSensitive) === "boolean" ? "caseSensitive" : void 0]: (_b2 = column.filterable) == null ? void 0 : _b2.caseSensitive,
        // Only inject a filterBy configuration if the column has a custom filterBy
        [filterFn ? "filterBy" : "_"]: function(record) {
          return filterFn({
            value: this.value,
            record,
            operator: this.operator,
            property: this.property,
            column
          });
        }
      }, true);
    }
    store.filter({ silent });
    column.$isApplyingFilter = false;
    if ((_c2 = field.picker) == null ? void 0 : _c2.isVisible) {
      (_e = (_d = field.picker) == null ? void 0 : _d.realign) == null ? void 0 : _e.call(_d);
    }
  }
  //endregion
  //region Menu items
  /**
   * Adds a menu item to toggle filter bar visibility.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Object<String,MenuItemConfig|Boolean|null>} options.items A named object to describe menu items
   * @internal
   */
  populateHeaderMenu({ items: items2 }) {
    items2.toggleFilterBar = {
      text: this.hidden ? "L{enableFilterBar}" : "L{disableFilterBar}",
      localeClass: this,
      weight: 120,
      icon: "b-fw-icon b-icon-filter",
      cls: "b-separator",
      onItem: () => this.toggleFilterBar()
    };
  }
  //endregion
  canFilterColumn(column) {
    var _a5, _b2;
    return column.filterable !== false && (((_a5 = column.filterable) == null ? void 0 : _a5.filterBar) === true || ((_b2 = column.filterable) == null ? void 0 : _b2.filterBar) !== false && this.defaultEnabled !== false);
  }
};
//region Config
__publicField(FilterBar, "$name", "FilterBar");
FilterBar.featureClass = "b-filter-bar";
FilterBar._$name = "FilterBar";
GridFeatureManager.registerFeature(FilterBar);

// ../Grid/lib/Grid/feature/Group.js
var Group = class extends InstancePlugin {
  static get configurable() {
    return {
      /**
       * The name of the record field to group by.
       * @prp {String}
       */
      field: null,
      /**
       * The icon to use for the collapse icon in collapsed state. Not applicable when using Scheduler in vertical
       * mode.
       * @config {String}
       */
      expandIconCls: "b-icon-group-expand",
      /**
       * The icon to use for the collapse icon in expanded state. Not applicable when using Scheduler in vertical
       * mode.
       * @config {String}
       */
      collapseIconCls: "b-icon-group-collapse",
      /**
       * The sort direction of the groups.
       * @prp {Boolean}
       * @default
       */
      ascending: true,
      /**
       * The height of group header rows. Can also be set on a per-group-header basis using the
       * {@link #config-renderer}. Not applicable when using Scheduler in vertical mode.
       * @prp {Number}
       */
      headerHeight: null,
      /**
       * Set to `true` to show the number of members of each group in the group header. Not applicable when using
       * Scheduler in vertical mode.
       * @prp {Boolean}
       */
      showCount: true,
      /**
       * A function used to sort the groups.
       * When grouping, the records have to be sorted so that records in a group stick together.
       * Technically that means that records having the same {@link #config-field} value
       * should go next to each other.
       * And this function (if provided) is responsible for applying such grouping order.
       * ```javascript
       * const grid = new Grid({
       *     features : {
       *         group : {
       *             // group by category
       *             field       : 'category',
       *             groupSortFn : (a, b) => {
       *                 const
       *                     aCategory = a.category || '',
       *                     bCategory = b.category || '';
       *
       *                 // 1st sort by "calegory" field
       *                 return aCategory > bCategory ? -1 :
       *                     aCategory < bCategory ? 1 :
       *                     // inside calegory groups we sort by "name" field
       *                     (a.name > b.name ? -1 : 1);
       *             }
       *         }
       *     }
       * });
       * ```
       *
       * @config {Function}
       * @param {GroupRecord} first The first group header record to compare
       * @param {GroupRecord} second The second group header record to compare
       * @returns {Number} Returns `1` if first value is greater than second value, `-1` if the opposite is true or `0` if they're equal
       */
      groupSortFn: null,
      /**
       * A function which produces the HTML for a group header.
       * The function is called in the context of this Group feature object.
       * Default group renderer displays the `groupRowFor` and `count`.
       *
       * <div class="note">
       * You should never modify any records inside this method.
       * </div>
       *
       * @config {Function}
       * @param {Object} renderData Object containing renderer parameters
       * @param {*} renderData.groupRowFor The value of the `field` for the group. Type depends on `field` used for grouping
       * @param {Core.data.Model} renderData.record The group record representing the group
       * @param {Number} renderData.count Number of records in the group
       * @param {Grid.column.Column} renderData.column The column the renderer runs for
       * @param {Boolean} renderData.isFirstColumn True, if `column` is the first column. If `RowNumberColumn` is the real first column, it's not taken into account
       * @param {Grid.column.Column} renderData.groupColumn The column under which the `field` is shown
       * @param {Object} renderData.size Sizing information for the group header row, only `height` is relevant
       * @param {Number} renderData.size.height The height of the row, set this if you want a custom height for the group header row.
       *   That is UI part, so do not rely on its existence
       * @param {Grid.view.Grid} renderData.grid The owning grid
       * @param {HTMLElement} renderData.rowElement The owning row element
       * @returns {DomConfig|String|null}
       * @default
       *
       * @category Rendering
       */
      renderer: null,
      /**
       * See {@link #keyboard-shortcuts Keyboard shortcuts} for details. Not applicable when using Scheduler in
       * vertical mode.
       * @config {Object<String,KeyMapConfig>}
       */
      keyMap: {
        " ": "toggleGroup"
      },
      /**
       * By default, clicking anywhere in a group row toggles its expanded/collapsed state. Not applicable when
       * using Scheduler in vertical mode.
       *
       * Configure this as `false` to limit this to only toggling on click of the expanded/collapsed
       * state icon.
       * @prp {Boolean}
       * @default
       */
      toggleOnRowClick: true
    };
  }
  //region Init
  construct(grid, config) {
    var _a5;
    const me = this;
    if (grid.features.tree) {
      return;
    }
    me._thisIsAUsedExpression(grid.features.groupSummary);
    config = me.processConfig(config);
    me.grid = grid;
    super.construct(grid, config);
    grid.ion({
      lockRows: "onLockRows",
      beforeUnlockRows: "onUnlockRows",
      thisObj: me
    });
    if (!((_a5 = grid.features.lockRows) == null ? void 0 : _a5.enabled) || grid.splitFrom) {
      me.init(grid);
    }
  }
  init(grid) {
    this.grid = grid;
    this.bindStore(grid.store);
    this.detachListeners("rowManager");
    grid.rowManager.ion({
      name: "rowManager",
      beforeRenderRow: "onBeforeRenderRow",
      renderCell: "renderCell",
      // The feature gets to see cells being rendered before the GroupSummary feature
      // because this injects header content into group header rows and adds rendering
      // info to the cells renderData which GroupSummary must comply with.
      prio: 1100,
      thisObj: this
    });
    if (!grid.isConfiguring) {
      grid.renderRows();
    }
  }
  // Group feature handles special config cases, where user can supply a string or a group config object
  // instead of a normal config object
  processConfig(config) {
    if (typeof config === "string") {
      return {
        field: config
      };
    }
    return config;
  }
  // override setConfig to process config before applying it (used mainly from ReactGrid)
  setConfig(config) {
    if (config === null) {
      this.store.clearGroupers();
    } else {
      super.setConfig(this.processConfig(config));
    }
  }
  bindStore(store) {
    const me = this, { client } = me;
    if (client.isSchedulerBase && client.isVertical && store) {
      store = client.resourceStore;
    }
    me.detachListeners("store");
    if (client.isLockedRows) {
      return;
    }
    store.ion({
      name: "store",
      group: "onStoreGroup",
      change: "onStoreChange",
      toggleGroup: "onStoreToggleGroup",
      thisObj: me
    });
    if ((me.field || store.isGrouped) && !client.isConfiguring) {
      store.group({
        field: me.field,
        ascending: me.ascending,
        fn: me.groupSortFn
      });
    } else {
      me.onStoreGroup({ groupers: store.groupers });
    }
  }
  updateRenderer(renderer) {
    this.groupRenderer = renderer;
  }
  updateField(field) {
    var _a5, _b2;
    const me = this;
    if (((_a5 = me.client.features.lockRows) == null ? void 0 : _a5.enabled) && !me.client.splitFrom) {
      return;
    }
    if (!me.isConfiguring || !((_b2 = me.store.groupers) == null ? void 0 : _b2.some((g) => g.field === field))) {
      me.store.group({
        field,
        ascending: me.ascending,
        fn: me.groupSortFn
      });
    }
  }
  updateGroupSortFn(fn2) {
    if (!this.isConfiguring) {
      this.store.group({
        field: this.field,
        ascending: this.ascending,
        fn: fn2
      });
    }
  }
  updateAscending(ascending2) {
    const me = this;
    if (!me.isConfiguring) {
      me.store.group({
        field: me.field,
        ascending: ascending2,
        fn: me.groupSortFn
      });
    }
  }
  doDisable(disable) {
    const { store } = this;
    if (disable && store.isGrouped) {
      const { sorters } = store;
      sorters.unshift(...store.groupers);
      this.currentGroupers = store.groupers;
      store.clearGroupers();
      store.sort(sorters);
    } else if (!disable && this.currentGroupers) {
      store.group(this.currentGroupers[0]);
      this.currentGroupers = null;
    }
    super.doDisable(disable);
  }
  get store() {
    const { grid } = this;
    if (grid.isSchedulerBase && grid.isVertical) {
      return grid.resourceStore;
    }
    return grid.store;
  }
  //endregion
  //region Plugin config
  // Plugin configuration. This plugin chains some of the functions in Grid.
  static get pluginConfig() {
    return {
      assign: ["collapseAll", "expandAll"],
      chain: [
        "renderHeader",
        "populateHeaderMenu",
        "getColumnDragToolbarItems",
        "onElementTouchStart",
        "onElementClick",
        "bindStore"
      ]
    };
  }
  //endregion
  //region Expand/collapse
  refreshGrid(groupRecord) {
    const { store, rowManager } = this.grid;
    if (rowManager.rowCount > store.count || !rowManager.getRowFor(groupRecord)) {
      rowManager.renderFromRow();
    } else {
      rowManager.renderFromRecord(groupRecord);
    }
  }
  /**
   * Collapses or expands a group header record (you can also pass a record that is part of a group) depending on its
   * current state.
   * @param {Core.data.Model|String} recordOrId Record or records id for a group row to collapse or expand
   * @param {Boolean} collapse Force collapse (`true`) or expand (`false`)
   * @fires toggleGroup
   */
  toggleCollapse(recordOrId, collapse) {
    this.internalToggleCollapse(recordOrId, collapse);
  }
  /**
   * Collapses or expands a group depending on its current state
   * @param {Core.data.Model|String} recordOrId Record or records id for a group row to collapse or expand
   * @param {Boolean} collapse Force collapse (true) or expand (true)
   * @param {Boolean} [skipRender] True to not render rows
   * @param {Event} [domEvent] The user interaction event (eg a `click` event) if the toggle request was
   * instigated by user interaction.
   * @param {Boolean} [allRecords] True if this event is part of toggling all groups
   * @internal
   * @fires toggleGroup
   */
  internalToggleCollapse(recordOrId, collapse, skipRender = false, domEvent, allRecords = false) {
    const me = this, { store, grid } = me;
    let indexOfTop = Number.MAX_VALUE;
    const groupRecords = ArrayHelper.asArray(recordOrId).map((groupRecord) => {
      groupRecord = store.getById(groupRecord);
      if (!groupRecord.isGroupHeader) {
        groupRecord = store.getGroupHeaderForRecord(groupRecord);
      }
      indexOfTop = Math.min(indexOfTop, store.indexOf(groupRecord), 0);
      if (!groupRecord.isGroupHeader) {
        return;
      }
      return groupRecord;
    });
    collapse = collapse === void 0 ? !groupRecords[0].meta.collapsed : collapse;
    if (grid.trigger("beforeToggleGroup", { groupRecord: groupRecords[0], groupRecords, collapse, domEvent }) === false) {
      return;
    }
    me.isToggling = true;
    if (collapse) {
      store.collapse(groupRecords);
    } else {
      store.expand(groupRecords);
    }
    me.isToggling = false;
    if (!skipRender) {
      me.refreshGrid(store.getAt(indexOfTop));
    }
    grid.trigger("toggleGroup", { groupRecord: groupRecords[0], groupRecords, collapse, allRecords });
    grid.afterToggleGroup();
  }
  /**
   * Collapse all groups. This function is exposed on Grid and can thus be called as `grid.collapseAll()`
   * @on-owner
   * @non-lazy-load
   * @typings {Promise<void>}
   * @category Grouping
   */
  collapseAll() {
    const me = this, { store, grid } = me;
    if (store.isGrouped && !me.disabled) {
      me.internalToggleCollapse(store.groupHeaderRecords, true, true, void 0, true);
      grid.trigger("collapseAllGroups");
      grid.refreshRows(null, true);
    }
  }
  /**
   * Expand all groups. This function is exposed on Grid and can thus be called as `grid.expandAll()`
   * @on-owner
   * @non-lazy-load
   * @typings {Promise<void>}
   * @category Grouping
   */
  expandAll() {
    const me = this, { store, grid } = me;
    if (store.isGrouped && !me.disabled) {
      me.internalToggleCollapse(store.groupHeaderRecords, false, true, void 0, true);
      grid.trigger("expandAllGroups");
      grid.refreshRows();
    }
  }
  //endregion
  //region Rendering
  onLockRows({ clone }) {
    this.grid = clone;
  }
  onUnlockRows({ source }) {
    const { client } = this;
    if (!client.isDestroying) {
      this.init(client);
    }
  }
  /**
   * Called before rendering row contents, used to reset rows no longer used as group rows
   * @private
   */
  onBeforeRenderRow({ row }) {
    const oldRecord = row.grid.store.getById(row.id);
    row.forceInnerHTML = row.forceInnerHTML || (oldRecord == null ? void 0 : oldRecord.isGroupHeader);
  }
  /**
   * Called when a cell is rendered, styles the group rows first cell.
   * @private
   */
  renderCell(renderData) {
    var _a5;
    const me = this, {
      cellElement,
      row,
      column,
      grid
    } = renderData, { meta } = renderData.record, rowClasses = {
      "b-group-row": 0,
      "b-grid-group-collapsed": 0
    };
    if (!me.disabled && me.store.isGrouped && "groupRowFor" in meta) {
      if (column.groupHeaderReserved) {
        return;
      }
      column.clearCell(cellElement);
      rowClasses["b-grid-group-collapsed"] = meta.collapsed;
      rowClasses["b-group-row"] = 1;
      if (grid.buildGroupHeader) {
        grid.buildGroupHeader(renderData);
      } else {
        me.buildGroupHeader(renderData);
      }
      if (column === me.groupHeaderColumn) {
        DomHelper.createElement({
          parent: cellElement,
          tag: "i",
          className: {
            "b-group-state-icon": 1,
            "b-fw-icon": 1,
            [me.expandIconCls]: meta.collapsed,
            [me.collapseIconCls]: !meta.collapsed
          },
          nextSibling: cellElement.firstChild
        });
        cellElement.classList.add("b-group-title");
        cellElement.$groupHeader = cellElement._hasHtml = true;
      }
    } else if (cellElement.$groupHeader) {
      (_a5 = cellElement.querySelector(".b-group-state-icon")) == null ? void 0 : _a5.remove();
      cellElement.classList.remove("b-group-title");
      cellElement.$groupHeader = false;
    }
    row.assignCls(rowClasses);
  }
  // renderData.cellElement is required
  buildGroupHeader(renderData) {
    var _a5;
    const me = this, {
      record,
      cellElement,
      column,
      persist,
      isExporting = false
    } = renderData, { grid } = me, meta = record.meta, groupRowFor = meta.emptyArray ? grid.L("L{Object.None}") : meta.groupRowFor, { groupSummary } = grid.features, count = meta.childCount - (groupSummary && groupSummary.target !== "header" ? 1 : 0);
    let html = null, applyDefault = true;
    if (persist || column) {
      const groupColumn = grid.columns.get(meta.groupField), isGroupHeaderColumn = renderData.isFirstColumn = column === me.findGroupHeaderColumn(isExporting);
      if (groupColumn == null ? void 0 : groupColumn.groupRenderer) {
        if (isGroupHeaderColumn) {
          html = groupColumn.groupRenderer({
            ...renderData,
            groupRowFor,
            groupRecords: record.groupChildren,
            groupColumn,
            count
          });
          applyDefault = false;
        }
      } else if (me.groupRenderer) {
        html = me.groupRenderer({
          ...renderData,
          groupRowFor,
          groupRecords: record.groupChildren,
          groupColumn,
          count,
          isFirstColumn: isGroupHeaderColumn
        });
      }
      if (!((_a5 = renderData.size) == null ? void 0 : _a5.height) && me.headerHeight) {
        renderData.size.height = me.headerHeight;
      }
      if (isGroupHeaderColumn && html == null && applyDefault && DomHelper.getChildElementCount(cellElement) === 0) {
        html = StringHelper.encodeHtml(`${groupRowFor === "__novalue__" ? "" : groupRowFor}` + (me.showCount ? ` (${count})` : ""));
      }
    } else if (me.groupRenderer) {
      html = me.groupRenderer(renderData);
    }
    if (typeof html === "string") {
      cellElement.innerHTML = html;
    } else if (typeof html === "object") {
      DomSync.sync({
        targetElement: cellElement,
        domConfig: {
          onlyChildren: true,
          children: ArrayHelper.asArray(html)
        }
      });
    }
    if (DomHelper.getChildElementCount(cellElement) > 0) {
      cellElement._hasHtml = true;
    }
    return cellElement.innerHTML;
  }
  get groupHeaderColumn() {
    return this.grid.columns.visibleColumns.find((column) => !column.groupHeaderReserved);
  }
  findGroupHeaderColumn(isExporting = false) {
    return this.grid.columns.visibleColumns.find((column) => {
      return !column.groupHeaderReserved && (isExporting ? column.exportable !== false : true);
    });
  }
  /**
   * Called when a header is rendered, adds grouping icon if grouped by that column.
   * @private
   * @param headerContainerElement
   */
  renderHeader(headerContainerElement = this.grid.headerContainer) {
    var _a5;
    const { store, grid } = this;
    if (headerContainerElement && store.isGrouped) {
      for (const groupInfo of store.groupers) {
        const column = grid.columns.get(groupInfo.field), header = column && grid.getHeaderElement(column.id);
        header == null ? void 0 : header.classList.add("b-group", groupInfo.ascending ? "b-asc" : "b-desc");
        if (header && (!((_a5 = grid.features.sort) == null ? void 0 : _a5.enabled) || column.sortable === false)) {
          const textEl = column.textWrapper;
          if (!(textEl == null ? void 0 : textEl.querySelector(".b-sort-icon"))) {
            DomHelper.createElement({
              parent: textEl,
              className: "b-icon b-sort-icon"
            });
          }
        }
      }
    }
  }
  //endregion
  //region Context menu
  /**
   * Supply items for headers context menu.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Object<String,MenuItemConfig|Boolean|null>} options.items A named object to describe menu items
   * @internal
   */
  populateHeaderMenu({ column, items: items2 }) {
    const me = this;
    if (column.groupable !== false) {
      items2.groupAsc = {
        text: "L{groupAscending}",
        localeClass: me,
        icon: "b-fw-icon b-icon-group-asc",
        cls: "b-separator",
        weight: 400,
        disabled: me.disabled,
        onItem: () => me.store.group(column.field, true)
      };
      items2.groupDesc = {
        text: "L{groupDescending}",
        localeClass: me,
        icon: "b-fw-icon b-icon-group-desc",
        weight: 410,
        disabled: me.disabled,
        onItem: () => me.store.group(column.field, false)
      };
    }
    if (me.store.isGrouped) {
      items2.groupRemove = {
        text: "L{stopGrouping}",
        localeClass: me,
        icon: "b-fw-icon b-icon-clear",
        cls: column.groupable ? "" : "b-separator",
        weight: 420,
        disabled: me.disabled,
        onItem: () => me.store.clearGroupers()
      };
    }
  }
  /**
   * Supply items to ColumnDragToolbar
   * @private
   */
  getColumnDragToolbarItems(column, items2) {
    var _a5;
    const me = this, { store, disabled } = me;
    items2.push({
      text: "L{groupAscendingShort}",
      group: "L{group}",
      localeClass: me,
      icon: "b-icon b-icon-group-asc",
      ref: "groupAsc",
      cls: "b-separator",
      weight: 110,
      disabled,
      onDrop: ({ column: column2 }) => store.group(column2.field, true)
    });
    items2.push({
      text: "L{groupDescendingShort}",
      group: "L{group}",
      localeClass: me,
      icon: "b-icon b-icon-group-desc",
      ref: "groupDesc",
      weight: 110,
      disabled,
      onDrop: ({ column: column2 }) => store.group(column2.field, false)
    });
    const grouped = ((_a5 = store.groupers) == null ? void 0 : _a5.some((col) => col.field === column.field)) && !disabled;
    items2.push({
      text: "L{stopGroupingShort}",
      group: "L{group}",
      localeClass: me,
      icon: "b-icon b-icon-clear",
      ref: "groupRemove",
      disabled: !grouped,
      weight: 110,
      onDrop: ({ column: column2 }) => store.removeGrouper(column2.field)
    });
    return items2;
  }
  //endregion
  //region Events - Store
  /**
   * Called when store grouping changes. Reflects on header and rerenders rows.
   * @private
   */
  onStoreGroup({ groupers }) {
    const { grid } = this, { element } = grid, curGroupHeaders = element && DomHelper.children(element, ".b-grid-header.b-group");
    if (element) {
      for (const header of curGroupHeaders) {
        header.classList.remove("b-group", "b-asc", "b-desc");
      }
      if (groupers) {
        this.renderHeader();
      }
    }
  }
  onStoreChange({ action, records }) {
    const { client } = this, { rowManager, store } = client;
    if (store.isGrouped && action === "move") {
      const { field } = store.groupers[0], fromRow = Math.min(...records.reduce((result, record) => {
        result.push(store.indexOf(record.instanceMeta(store).groupParent));
        if (field in record.meta.modified) {
          const oldGroup = store.groupRecords.find((r) => r.meta.groupRowFor === record.meta.modified[field]);
          if (oldGroup) {
            result.push(store.indexOf(oldGroup));
          }
        }
        return result;
      }, []));
      rowManager.renderFromRow(rowManager.getRow(fromRow));
    }
  }
  // React to programmatic expand/collapse
  onStoreToggleGroup({ groupRecord }) {
    if (!this.isToggling) {
      this.refreshGrid(groupRecord);
    }
  }
  //endregion
  //region Events - Grid
  /**
   * Store touches when user touches header, used in onElementTouchEnd.
   * @private
   */
  onElementTouchStart(event) {
    const me = this, { target } = event, header = target.closest(".b-grid-header"), column = header && me.grid.getColumnFromElement(header);
    if (event.touches.length > 1 && column && column.groupable !== false && !me.disabled) {
      me.store.group(column.field);
    }
  }
  /**
   * React to click on headers (to group by that column if [alt] is pressed) and on group rows (expand/collapse).
   * @private
   * @param event
   * @returns {Boolean}
   */
  onElementClick(event) {
    var _a5;
    const me = this, { store } = me, { target } = event, row = target.closest(".b-group-row"), header = target.closest(".b-grid-header"), field = header == null ? void 0 : header.dataset.column;
    if (target.classList.contains("b-resizer") || me.disabled || target.classList.contains("b-action-item") || target.closest(".b-checkbox") || event.handled) {
      return;
    }
    if (header && field) {
      const columnGrouper = (_a5 = store.groupers) == null ? void 0 : _a5.find((g) => g.field === field);
      if (columnGrouper && !event.shiftKey) {
        columnGrouper.ascending = !columnGrouper.ascending;
        store.group();
        return false;
      } else if (event.shiftKey) {
        const column = me.grid.columns.get(field);
        if (column.groupable !== false) {
          if (event.altKey) {
            store.removeGrouper(field);
          } else {
            store.group(field);
          }
        }
      }
    }
    if (row && (me.toggleOnRowClick || event.target.classList.contains("b-group-state-icon"))) {
      me.internalToggleCollapse(DomDataStore.get(row).id, void 0, void 0, event);
      return false;
    }
  }
  /**
   * Toggle groups with [space].
   * @private
   */
  toggleGroup() {
    var _a5;
    const { grid } = this, { focusedCell } = grid;
    if (!this.disabled && !focusedCell.isActionable && ((_a5 = focusedCell.record) == null ? void 0 : _a5.isGroupHeader)) {
      this.internalToggleCollapse(focusedCell.id);
      return true;
    }
    return false;
  }
  //endregion
  updateHeaderHeight() {
    if (!this.isConfiguring) {
      this.client.refreshRows();
    }
  }
  updateShowCount() {
    if (!this.isConfiguring) {
      this.client.refreshRows();
    }
  }
};
__publicField(Group, "$name", "Group");
Group._$name = "Group";
GridFeatureManager.registerFeature(Group, true, ["Grid", "Scheduler"]);
GridFeatureManager.registerFeature(Group, false, ["TreeGrid"]);

// ../Grid/lib/Grid/feature/mixin/SummaryFormatter.js
var SummaryFormatter_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    /**
     * Calculates sums and returns as a html table
     * @param {Grid.column.Column} column Column to calculate sum for
     * @param {Core.data.Model[]} records Records to include in calculation
     * @param {String} cls CSS class to apply to summary table
     * @returns {String} html content
     */
    generateHtml(column, records, cls) {
      const { store } = this.client, summaries = column.summaries || (column.sum ? [{ sum: column.sum, renderer: column.summaryRenderer }] : []);
      let html = `<div class="b-summary-wrap ${cls}">`;
      summaries.forEach((config) => {
        var _a6, _b2;
        let type = config.sum, sum = null;
        if (type === true)
          type = "sum";
        switch (type) {
          case "sum":
          case "add":
            sum = store.sum(column.field, records);
            break;
          case "max":
            sum = store.max(column.field, records);
            break;
          case "min":
            sum = store.min(column.field, records);
            break;
          case "average":
          case "avg":
            sum = store.average(column.field, records);
            break;
          case "count":
            sum = records.length;
            break;
          case "countNotEmpty":
            sum = records.reduce((sum2, record) => {
              const value2 = record.getValue(column.field);
              return sum2 + (value2 != null ? 1 : 0);
            }, 0);
            break;
        }
        if (typeof type === "function") {
          sum = records.reduce(type, "seed" in config ? config.seed : 0);
        }
        if (sum !== null) {
          const valueCls = "b-grid-summary-value", labelHtml = config.label ? `<div class="b-grid-summary-label">${config.label}</div>` : "";
          let valueHtml = config.renderer ? config.renderer({ config, sum }) : (_b2 = (_a6 = column.formatValue) == null ? void 0 : _a6.call(column, sum, records[0])) != null ? _b2 : sum, summaryHtml;
          if (valueHtml == null) {
            valueHtml = "";
          }
          if (!String(valueHtml).includes("<div>")) {
            summaryHtml = labelHtml ? `${labelHtml}<div class="${valueCls}">${valueHtml}</div>` : `<div class="${valueCls} b-nolabel">${valueHtml}</div>`;
          } else {
            summaryHtml = valueHtml;
          }
          html += summaryHtml;
        }
      });
      return `${html}</div>`;
    }
  }, __publicField(_a5, "$name", "SummaryFormatter"), _a5;
};

// ../Grid/lib/Grid/feature/GroupSummary.js
var GroupSummary = class extends SummaryFormatter_default(InstancePlugin) {
  static get configurable() {
    return {
      /**
       * Set to `true` to have group summaries rendered in the group header when a group is collapsed.
       *
       * Only applies when {@link #config-target} is `'footer'` (the default).
       *
       * @member {Boolean} collapseToHeader
       */
      /**
       * Configure as `true` to have group summaries rendered in the group header when a group is collapsed.
       *
       * ```javascript
       * const grid = new Grid({
       *    features : {
       *        groupSummary : {
       *            collapseToHeader : true
       *        }
       *    }
       * });
       * ```
       *
       * Only applies when {@link #config-target} is `'footer'` (the default).
       *
       * @config {Boolean}
       */
      collapseToHeader: null,
      /**
       * Where to render the group summaries to, either `header` to display them in the group header or `footer`
       * to display them in the group footer (the default).
       *
       * @member {'header'|'footer'} target
       */
      /**
       * Where to render the group summaries to, either `header` to display them in the group header or `footer`
       * to display them in the group footer (the default).
       *
       * ```javascript
       * const grid = new Grid({
       *    features : {
       *        groupSummary : {
       *            target : 'header'
       *        }
       *    }
       * });
       * ```
       *
       * @config {'header'|'footer'}
       * @default
       */
      target: "footer"
    };
  }
  construct(grid, config) {
    super.construct(grid, config);
    if (!grid.features.group) {
      throw new Error("Requires Group feature to work, please enable");
    }
    this.bindStore(grid.store);
    grid.rowManager.ion({
      beforeRenderRow: "onBeforeRenderRow",
      renderCell: "renderCell",
      // The feature gets to see cells being rendered after the Group feature
      // because the Group feature injects header content into group header rows
      // and adds rendering info to the cells renderData which we must comply with.
      // In particular, it calculates the isFirstColumn flag which it adds to
      // the cell renderData which we interrogate.
      prio: 1e3,
      thisObj: this
    });
  }
  bindStore(store) {
    this.detachListeners("store");
    store.ion({
      name: "store",
      update: "onStoreUpdate",
      // need to run before grids listener, to flag for full refresh
      prio: 1,
      thisObj: this
    });
  }
  get store() {
    return this.client.store;
  }
  doDisable(disable) {
    this.updateTarget(this.target);
    super.doDisable(disable);
  }
  changeTarget(target) {
    ObjectHelper.assertString(target, "target");
    return target;
  }
  updateTarget(target) {
    this.store.useGroupFooters = !this.disabled && target === "footer";
    if (!this.isConfiguring) {
      this.store.group();
    }
  }
  changeCollapseToHeader(collapseToHeader) {
    ObjectHelper.assertBoolean(collapseToHeader, "collapseToHeader");
    return collapseToHeader;
  }
  updateCollapseToHeader() {
    if (!this.isConfiguring) {
      this.store.group();
    }
  }
  //endregion
  //region Plugin config
  // Plugin configuration. This plugin chains some of the functions in Grid.
  static get pluginConfig() {
    return {
      chain: ["bindStore"]
    };
  }
  //endregion
  //region Render
  /**
   * Called before rendering row contents, used to reset rows no longer used as group summary rows
   * @private
   */
  onBeforeRenderRow({ row, record }) {
    if (row.isGroupFooter && !("groupFooterFor" in record.meta)) {
      row.isGroupFooter = false;
      row.forceInnerHTML = true;
    } else if (row.isGroupHeader && !record.meta.collapsed) {
      row.eachElement(this.removeSummaryElements);
    }
  }
  removeSummaryElements(rowEl) {
  }
  /**
   * Called when a cell is rendered, styles the group rows first cell.
   * @private
   */
  renderCell({ column, cellElement, row, record, size, isFirstColumn }) {
    const me = this, { meta } = record, { rowHeight } = me.client, isGroupHeader = "groupRowFor" in meta, isGroupFooter = "groupFooterFor" in meta, targetsHeader = me.target === "header", rowClasses = {
      "b-group-footer": 0,
      "b-header-summary": 0
    }, isSummaryTarget = (
      // Header cell should have summary content if we are targeting the header or if the group is collapsed
      // and we are configured with collapseToHeader, excluding the first column which holds the group title
      isGroupHeader && (targetsHeader || me.collapseToHeader && meta.collapsed) && !isFirstColumn || // Footer cell should have summary content if we are targeting the footer (won't render if collapsed)
      isGroupFooter && !targetsHeader
    );
    if (isGroupHeader || isGroupFooter) {
      size.height = isGroupHeader ? size.height || rowHeight : rowHeight;
    }
    if (me.store.isGrouped && isSummaryTarget && !me.disabled) {
      column.clearCell(cellElement);
      const groupRecord = isGroupHeader ? record : meta.groupRecord;
      row.isGroupFooter = isGroupFooter;
      row.isGroupHeader = isGroupHeader;
      if (isGroupFooter) {
        rowClasses["b-group-footer"] = 1;
      } else {
        rowClasses["b-header-summary"] = 1;
      }
      const heightSetting = me.updateSummaryHtml(cellElement, column, groupRecord), count = typeof heightSetting === "number" ? heightSetting : heightSetting.count;
      if (count > 1) {
        size.height += meta.collapsed && !targetsHeader ? 0 : count * rowHeight * 0.1;
      }
      if (heightSetting.height) {
        size.height += heightSetting.height;
      }
    }
    row.assignCls(rowClasses);
  }
  updateSummaryHtml(cellElement, column, groupRecord) {
    const records = groupRecord.groupChildren.slice();
    if (records[records.length - 1].isGroupFooter) {
      records.pop();
    }
    const html = this.generateHtml(column, records, "b-grid-group-summary", groupRecord, groupRecord.meta.groupField, groupRecord.meta.groupRowFor);
    if (!cellElement.children.length) {
      cellElement.innerHTML = html;
    } else {
      DomHelper.sync(html, cellElement.firstElementChild);
    }
    return column.summaries ? column.summaries.length : column.sum ? 1 : 0;
  }
  //endregion
  //region Events
  /**
   * Updates summaries on store changes (except record update, handled below)
   * @private
   */
  onStoreUpdate({ source: store, changes }) {
    if (!this.disabled && store.isGrouped) {
      if (changes && store.groupers.find((grouper) => grouper.field in changes)) {
        return;
      }
      const shouldUpdate = Object.keys(changes).some((field) => {
        const colField = this.client.columns.get(field);
        return Boolean(colField) && (Boolean(colField.sum) || Boolean(colField.summaries));
      });
      if (shouldUpdate) {
        this.client.forceFullRefresh = true;
      }
    }
  }
  //endregion
  /**
   * Refreshes the summaries
   */
  refresh() {
    this.client.columns.visibleColumns.forEach((column) => {
      if (column.hasSummary) {
        this.client.refreshColumn(column);
      }
    });
  }
  hasSummary(column) {
    return column.sum || column.summaries;
  }
  updateSummaries() {
    if (!this.isConfiguring) {
      this.refresh();
    }
  }
};
//region Init
__publicField(GroupSummary, "$name", "GroupSummary");
GroupSummary.featureClass = "b-group-summary";
GroupSummary._$name = "GroupSummary";
GridFeatureManager.registerFeature(GroupSummary);

// ../Grid/lib/Grid/feature/Split.js
var startScrollOptions = Object.freeze({
  animate: false,
  block: "start"
});
var endScrollOptions = Object.freeze({
  animate: false,
  block: "end"
});
var splitterWidth = 7;
var ignoreListeners = {
  split: 1,
  unsplit: 1
};
var _ignoreColumnChanges;
var Split = class extends InstancePlugin {
  constructor() {
    super(...arguments);
    // Flag used to ignore column changes that arise from syncing columns
    __privateAdd(this, _ignoreColumnChanges, false);
    __publicField(this, "restorers", []);
  }
  static get pluginConfig() {
    return {
      chain: ["populateCellMenu", "afterConfigChange", "afterAddListener", "afterRemoveListener", {
        fn: "onInternalPaint",
        prio: -1e3
      }],
      assign: ["split", "unsplit", "subViews", "syncSplits"]
    };
  }
  doDestroy() {
    this.unsplit(true);
    super.doDestroy();
  }
  doDisable(disable) {
    const me = this;
    if (!me.isConfiguring) {
      if (disable) {
        me._disabledSplitOptions = me._splitOptions;
        me.unsplit();
      } else if (me._disabledSplitOptions) {
        me.split(me._disabledSplitOptions);
        me._disabledSplitOptions = null;
      }
    }
  }
  //region Split / unsplit
  // Subclasses implement this, so our pluginConfig must add it to the chain because subclasses
  // cannot add to pluginConfig chains.
  onInternalPaint() {
  }
  get isSplit() {
    var _a5;
    return Boolean((_a5 = this.widgets) == null ? void 0 : _a5.length);
  }
  getClientConfig(appendTo, order, options, config = {}) {
    const { client } = this, { subGrids, regions } = client, columns = client.columns.records.slice(), subGridConfigs = ObjectHelper.assign({}, client.subGridConfigs);
    client.eachSubGrid((subGrid) => {
      const config2 = subGridConfigs[subGrid.region];
      if (subGrid.flex) {
        config2.flex = subGrid.flex;
      } else {
        config2.width = subGrid.element.style.width;
      }
    });
    if (options.atColumn && regions.length > 1 && order > 0) {
      const subGridIndex = regions.indexOf(options.atColumn.region);
      for (let i = 0; i < subGridIndex; i++) {
        const subGrid = subGrids[regions[i]];
        ArrayHelper.remove(columns, ...subGrid.columns.records);
        delete subGridConfigs[regions[i]];
      }
    }
    const clientConfig = ObjectHelper.merge({}, client.initialConfig, {
      appendTo,
      insertFirst: null,
      insertBefore: null,
      splitFrom: client,
      owner: client.owner,
      // Use no toolbar or fake empty toolbar for things to line up nicely
      tbar: client.initialConfig.tbar && order === 1 ? {
        height: client.tbar.height,
        items: [" "]
      } : null,
      // Share store & selection
      store: client.store,
      selectedRecordCollection: client.selectedRecordCollection,
      subGridConfigs,
      // Cannot directly share columns, since there is a 1-1 mapping between column and it's header
      columns: this.cloneColumns(columns),
      minHeight: 0,
      minWidth: 0
    }, config);
    for (const prop of ObjectHelper.keys(this.relayProperties)) {
      clientConfig[prop] = client[prop];
    }
    const appListeners = {};
    for (const name in client.listeners) {
      if (!ignoreListeners[name]) {
        const listeners = client.listeners[name].filter((l) => !l.$internal);
        if (listeners.length) {
          appListeners[name] = listeners;
        }
      }
    }
    clientConfig.listeners = appListeners;
    if (options.direction === "horizontal" || options.direction === "both" && order !== 1) {
      if (!("hideHeaders" in clientConfig)) {
        clientConfig.hideHeaders = true;
      }
      clientConfig.features.headerMenu = false;
    }
    delete clientConfig.data;
    delete clientConfig.adopt;
    return clientConfig;
  }
  cloneColumns(source) {
    return source.flatMap((col) => {
      if (col.meta.isSelectionColumn || col.field === "expanderActionColumn") {
        return [];
      }
      const data = { ...col.data };
      if (col.children) {
        data.children = col.children.map((child) => ({ ...child.data }));
      }
      delete data.headerRenderer;
      delete data.parentId;
      delete data.onBeforeWidgetSetValue;
      delete data.onAfterWidgetSetValue;
      return data;
    });
  }
  cloneClient(appendTo, order, options, config) {
    const clientConfig = this.getClientConfig(appendTo, order, options, config), clone = new this.client.constructor(clientConfig);
    clone.element.classList.add("b-split-clone");
    return clone;
  }
  // Process options, deducing direction, atRecord, etc.
  processOptions(options) {
    var _a5;
    const { client } = this, { atRecord, atColumn, direction } = options;
    if (!direction) {
      if (atRecord && atColumn) {
        options.direction = "both";
      } else if (atColumn) {
        options.direction = "vertical";
      } else {
        options.direction = "horizontal";
      }
    } else {
      if (direction !== "vertical" && !atRecord && client.store.count) {
        const centerY = client._bodyRectangle.height / 2 + client.scrollable.y, centerRow = (_a5 = client.rowManager.getRowAt(centerY, true)) != null ? _a5 : client.rowManager.rows[Math.ceil(client.rowManager.rows.length / 2)];
        options.atRecord = client.store.getById(centerRow.id);
      }
      if (direction !== "horizontal" && !atColumn) {
        const bounds = Rectangle.from(client.element);
        let centerX = bounds.center.x - bounds.x, subGrid = client.subGrids[client.regions[0]], i = 0, column = null;
        while (centerX > subGrid.width) {
          centerX -= subGrid.width;
          subGrid = client.subGrids[client.regions[++i]];
        }
        centerX += subGrid.scrollable.x;
        const { visibleColumns } = subGrid.columns;
        let x = 0, j = 0;
        while (x < centerX && j < visibleColumns.length) {
          column = visibleColumns[j++];
          x += column.element.offsetWidth;
        }
        options.atColumn = column;
      }
    }
    return options;
  }
  // Create element to contain the splits, it "both" mode it will hold a top container and a bottom container.
  // In single mode, it will hold the splits + splitters directly.
  createSplitContainer({ direction }) {
    const { client } = this, { element } = client;
    return this.splitContainer = DomHelper.createElement({
      parent: element.parentElement,
      nextSibling: element.nextSibling,
      className: {
        "b-split-container": 1,
        [`b-split-${direction}`]: 1,
        "b-rtl": client.rtl
      },
      style: {
        width: element.style.width,
        flex: element.style.flex,
        height: element.style.height
      },
      children: [
        // Split in one dir, use original as first child
        direction !== "both" && element,
        // Split in both directions, make two sub-containers and put original in first
        direction === "both" && {
          className: "b-split-top",
          children: [
            element
          ]
        },
        direction === "both" && {
          className: "b-split-bottom"
        }
      ]
    });
  }
  // Make the headers of all splits same height. Since headers shrinkwrap, they might differ depending on which
  // subgrids was cloned to each split
  syncHeaderHeights() {
    let maxHeaderHeight = 0;
    for (const split2 of this.subViews) {
      split2.eachSubGrid((subGrid) => {
        if (subGrid.header.height > maxHeaderHeight) {
          maxHeaderHeight = subGrid.header.height;
        }
      });
    }
    for (const split2 of this.subViews) {
      split2.eachSubGrid((subGrid) => {
        subGrid.header.height = maxHeaderHeight;
      });
    }
  }
  // Clones can be created with correct subgrids, in the original we might instead need to hide some when splitting
  // in a region that is not the last one (locked for example)
  toggleOriginalSubGrids(options) {
    const me = this, { client } = me, { regions } = client;
    if (options.atColumn && regions.length > 1) {
      const subGridIndex = regions.indexOf(options.atColumn.region), splits = [client];
      if (options.direction === "both") {
        splits.push(me.subViews[2]);
      }
      for (const split2 of splits) {
        if (subGridIndex + 1 < regions.length) {
          const isOriginal = split2 === client;
          const subGrid = split2.subGrids[regions[subGridIndex]];
          subGrid.hideSplitter();
          isOriginal && me.restorers.push(() => subGrid.showSplitter());
          if (!subGrid.flex) {
            client.inForEachOther = true;
            subGrid.flex = 1;
            client.inForEachOther = false;
            isOriginal && me.restorers.push(() => {
              subGrid.flex = null;
              subGrid.width = subGrid._initialWidth;
            });
          }
          for (let i = subGridIndex + 1; i < regions.length; i++) {
            const subGrid2 = split2.subGrids[regions[i]];
            subGrid2.hide();
            isOriginal && me.restorers.push(() => {
              subGrid2.show();
            });
          }
          if (regions.length === 2) {
            split2._initialWidth = split2.element.style.width;
            split2._initialFlex = split2.flex;
            split2.width = subGrid._initialWidth;
            isOriginal && me.restorers.push(() => {
              if (split2._initialFlex !== null) {
                split2.flex = split2._initialFlex;
              } else if (split2._initialWidth !== null) {
                split2.width = split2._initialWidth;
              }
            });
          }
        }
      }
    }
  }
  /**
   * Split the grid into two or four parts.
   *
   * - Splits into two when passed `direction : 'vertical'`, `direction : 'horizontal'` or `atColumn` or `atRecord`.
   * - Splits into four when passed `direction : 'both'` or `atColumn` and `atRecord`.
   *
   * ```javascript
   * // Split horizontally (at the row in the center of the grid)
   * await grid.split({ direction : 'horizontal' });
   *
   * // Split both ways by a specific column and record
   * await grid.split({
   *    atRecord : grid.store.getById(10),
   *    atColumn : grid.columns.get('city')
   * });
   * ```
   *
   * To return to a single grid, call {@link #function-unsplit}.
   *
   * Note that this function is callable directly on the grid instance.
   *
   * @param {Object} [options] Split options
   * @param {'vertical'|'horizontal'|'both'} [options.direction] Split direction, 'vertical', 'horizontal' or 'both'.
   * Not needed when passing `atColumn` or `atRecord`.
   * @param {Grid.column.Column} [options.atColumn] Column to split at
   * @param {Core.data.Model} [options.atRecord] Record to split at
   * @returns {Promise} Resolves when split is complete, and subviews are scrolled to the correct position.
   * @async
   * @on-owner
   * @category Common
   */
  async split(options = {}) {
    const me = this, { client } = me;
    if (client.splitFrom) {
      return;
    }
    if (me.isSplit) {
      await me.unsplit(true);
    }
    const { rtl } = client, { atRecord, atColumn, direction } = me.processOptions(options);
    let { splitX, remainingWidth } = options, splitY = null, remainingHeight = null;
    if (atRecord) {
      await client.scrollRowIntoView(atRecord);
      const row = client.getRowFor(atRecord);
      if (!row) {
        throw new Error(`Could not find row for record ${atRecord.id}`);
      }
      splitY = Rectangle.from(row.cells[0], client.element).bottom;
      remainingHeight = Rectangle.from(client.element).height - splitY;
    }
    if (atColumn && !splitX) {
      splitX = Rectangle.from(atColumn.element, client.element).getEnd(rtl);
      remainingWidth = Rectangle.from(client.element).width - splitX - DomHelper.scrollBarWidth;
      if (rtl) {
        const x = splitX;
        splitX = remainingWidth + DomHelper.scrollBarWidth;
        remainingWidth = x - DomHelper.scrollBarWidth;
      }
    }
    const scrollPromises = [], splitContainer = me.createSplitContainer(options), { visibleColumns } = client.columns, nextColumn = atColumn ? visibleColumns[visibleColumns.indexOf(atColumn) + 1] : null, nextRecord = atRecord ? client.store.getNext(atRecord) : null;
    client.eachSubGrid((subGrid) => subGrid._initialWidth = subGrid.width);
    client.columns.commit();
    if (direction !== "both") {
      const cloneConfig = {
        flex: `0 0 ${(splitY != null ? remainingHeight : remainingWidth) - splitterWidth}px`,
        height: null
      };
      const [, clone] = me.widgets = [
        new Splitter({ appendTo: splitContainer }),
        me.cloneClient(splitContainer, direction === "vertical" ? 1 : 0, options, cloneConfig)
      ];
      if (splitX != null) {
        client.renderRows();
        if (!options.atDate) {
          scrollPromises.push(client.scrollColumnIntoView(atColumn, endScrollOptions));
          nextColumn && scrollPromises.push(clone.scrollColumnIntoView(nextColumn, startScrollOptions));
        }
      }
      if (splitY != null) {
        scrollPromises.push(clone.scrollRowIntoView(nextRecord, startScrollOptions));
      }
      client.element.classList.add("b-split-start");
      clone.element.classList.add("b-split-end");
      client.scrollable.addPartner(clone.scrollable, {
        x: direction === "horizontal",
        y: direction !== "horizontal"
      });
    } else {
      const rightConfig = {
        flex: `0 0 ${remainingWidth - splitterWidth}px`
      };
      splitContainer.lastElementChild.style.flex = `0 0 ${remainingHeight - splitterWidth}px`;
      me.widgets = [
        new Splitter({ insertBefore: splitContainer.lastElementChild }),
        // Full horizontal
        me.topSplitter = new Splitter({ appendTo: splitContainer.firstElementChild }),
        // Top vertical
        me.cloneClient(splitContainer.firstElementChild, 1, options, rightConfig),
        // Top right
        me.cloneClient(splitContainer.lastElementChild, 0, options),
        // Bottom left
        me.bottomSplitter = new Splitter({ appendTo: splitContainer.lastElementChild }),
        // Bottom vertical
        me.cloneClient(splitContainer.lastElementChild, 2, options, rightConfig)
        // Bottom right
      ];
      const topLeft = client, topRight = me.widgets[2], bottomLeft = me.widgets[3], bottomRight = me.widgets[5];
      topLeft.element.classList.add("b-split-top-start");
      topRight.element.classList.add("b-split-top-end");
      bottomLeft.element.classList.add("b-split-bottom-start");
      bottomRight.element.classList.add("b-split-bottom-end");
      if (splitX != null) {
        topLeft.renderRows();
        bottomLeft.renderRows();
        if (atColumn && !options.atDate) {
          scrollPromises.push(client.scrollColumnIntoView(atColumn, endScrollOptions));
          nextColumn && scrollPromises.push(topRight.scrollColumnIntoView(nextColumn, startScrollOptions));
        }
      }
      if (splitY != null) {
        scrollPromises.push(
          bottomLeft.scrollRowIntoView(nextRecord, startScrollOptions),
          bottomRight.scrollRowIntoView(nextRecord, startScrollOptions)
        );
      }
      topLeft.scrollable.addPartner(topRight.scrollable, "y");
      topLeft.scrollable.addPartner(bottomLeft.scrollable, "x");
      topRight.scrollable.addPartner(bottomRight.scrollable, "x");
      bottomLeft.scrollable.addPartner(bottomRight.scrollable, "y");
      me.topSplitter.ion({
        splitterMouseDown: "onSplitterMouseDown",
        splitterMouseEnter: "onSplitterMouseEnter",
        splitterMouseLeave: "onSplitterMouseLeave",
        drag: "onSplitterDrag",
        drop: "onSplitterDrop",
        thisObj: me
      });
      me.bottomSplitter.ion({
        splitterMouseDown: "onSplitterMouseDown",
        splitterMouseEnter: "onSplitterMouseEnter",
        splitterMouseLeave: "onSplitterMouseLeave",
        drag: "onSplitterDrag",
        drop: "onSplitterDrop",
        thisObj: me
      });
    }
    me.subViews = [client, ...me.widgets.filter((w) => w.isGridBase)];
    me.toggleOriginalSubGrids(options);
    me.syncHeaderHeights();
    me._splitOptions = options;
    await Promise.all(scrollPromises);
    const bounds = Rectangle.from(client.element);
    client.onInternalResize(client.element, bounds.width, bounds.height);
    client.eachSubGrid((subGrid) => {
      const subGridBounds = Rectangle.from(subGrid.element);
      subGrid.onInternalResize(subGrid.element, subGridBounds.width, subGridBounds.height);
    });
    client.splitDirection = direction;
    client.scrollable.x += 0.5;
    client.scrollable.y += 0.5;
    me.startSyncingColumns();
    client.trigger("split", { subViews: me.subViews, options });
    return me.subViews;
  }
  /**
   * Remove splits, returning to a single grid.
   *
   * Note that this function is callable directly on the grid instance.
   *
   * @on-owner
   * @async
   * @category Common
   */
  async unsplit(silent = false) {
    var _a5;
    const me = this, {
      client,
      splitContainer
    } = me, { element } = client;
    if (me.isSplit) {
      me.stopSyncingColumns();
      delete client.splitDirection;
      (_a5 = me.widgets) == null ? void 0 : _a5.forEach((split2) => split2.destroy());
      me.widgets = null;
      client.eachSubGrid((subGrid) => subGrid.scrollable.x);
      client.scrollable.y;
      splitContainer.parentElement.appendChild(element);
      splitContainer.remove();
      me.splitContainer = null;
      element.style.flexBasis = element.style.flexGrow = "";
      element.classList.remove("b-split-top-start", "b-split-start");
      me.subViews.length = 0;
      if (!me.isDestroying) {
        client.renderRows();
        me.unsplitCleanup();
        for (const restorer of me.restorers) {
          restorer();
        }
        me.restorers.length = 0;
        await AsyncHelper.animationFrame();
        await AsyncHelper.animationFrame();
        if (me.isDestroyed) {
          return;
        }
        !silent && client.trigger("unsplit");
        me._splitOptions = null;
      }
    }
  }
  unsplitCleanup() {
  }
  //endregion
  //region Context menu
  populateCellMenu({ record, column, items: items2 }) {
    const me = this, { isSplit } = me, { splitFrom } = me.client;
    if (!me.disabled) {
      items2.splitGrid = {
        text: "L{split}",
        localeClass: me,
        icon: "b-icon b-icon-split-vertical",
        weight: 500,
        separator: true,
        hidden: isSplit || splitFrom,
        menu: {
          splitHorizontally: {
            text: "L{horizontally}",
            icon: "b-icon b-icon-split-horizontal",
            localeClass: me,
            weight: 100,
            onItem() {
              me.split({ atRecord: record });
            }
          },
          splitVertically: {
            text: "L{vertically}",
            icon: "b-icon b-icon-split-vertical",
            localeClass: me,
            weight: 200,
            onItem() {
              me.split({ atColumn: column });
            }
          },
          splitBoth: {
            text: "L{both}",
            icon: "b-icon b-icon-split-both",
            localeClass: me,
            weight: 300,
            onItem() {
              me.split({ atColumn: column, atRecord: record });
            }
          }
        }
      };
      items2.unsplitGrid = {
        text: "L{unsplit}",
        localeClass: me,
        icon: "b-icon b-icon-clear",
        hidden: !(isSplit || splitFrom),
        weight: 400,
        separator: true,
        onItem() {
          (splitFrom || me).unsplit();
        }
      };
    }
  }
  //endregion
  //region Syncing columns
  startSyncingColumns() {
    this.detachListeners("columns");
    for (const subView of this.subViews) {
      subView.columns.ion({
        name: "columns",
        change: "onColumnsChange",
        thisObj: this
      });
    }
  }
  stopSyncingColumns() {
    this.detachListeners("columns");
  }
  onColumnsChange({
    source,
    isMove,
    action,
    /*index, */
    parent,
    records,
    changes
  }) {
    var _a5, _b2;
    const me = this;
    if (!__privateGet(me, _ignoreColumnChanges)) {
      __privateSet(me, _ignoreColumnChanges, true);
      for (const clone of me.subViews) {
        const { columns } = clone;
        if (source !== columns) {
          if (action === "update" && changes.region && Object.keys(changes).length === 1 && !me.isLockRows) {
            if (!columns.getById(records[0].id)) {
              const [column] = records, targetParent = (_a5 = columns.getById(me.$before.parent.id)) != null ? _a5 : columns.rootNode, targetBefore = me.$before.id !== null && columns.getById(me.$before.id);
              targetParent.insertChild(column.data, targetBefore);
            } else {
              columns.remove(records[0].id);
            }
            me.$before = null;
          } else if (!(isMove == null ? void 0 : isMove[records[0].id]) && isMove !== true) {
            if (action === "add") {
              const relevantColumns = records.filter((column) => clone.getSubGridFromColumn(column));
              columns.add(me.cloneColumns(relevantColumns));
            } else {
              columns.applyChangesFromStore(source);
            }
          } else if (action === "add") {
            const sourceColumn = records[0], sourceBefore = sourceColumn.nextSibling, targetColumn = columns.getById(sourceColumn.id);
            if (!targetColumn) {
              me.$before = {
                id: sourceBefore == null ? void 0 : sourceBefore.id,
                parent
              };
              __privateSet(me, _ignoreColumnChanges, false);
              continue;
            }
            if (sourceColumn.meta.isSelectionColumn) {
              __privateSet(me, _ignoreColumnChanges, false);
              continue;
            }
            const targetParent = (_b2 = columns.getById(parent.id)) != null ? _b2 : columns.rootNode, targetBefore = sourceBefore && columns.getById(sourceBefore.id);
            targetParent.insertChild(targetColumn, targetBefore);
          }
          columns.commit();
        }
      }
      source.commit();
      __privateSet(me, _ignoreColumnChanges, false);
    }
  }
  //endregion
  //region Syncing splitters
  getOtherSplitter(splitter) {
    return splitter === this.topSplitter ? this.bottomSplitter : this.topSplitter;
  }
  onSplitterMouseDown({ source, event }) {
    if (!event.handled) {
      event.handled = true;
      this.getOtherSplitter(source).onMouseDown(event);
    }
  }
  onSplitterMouseEnter({ source, event }) {
    this.getOtherSplitter(source).element.classList.add("b-hover");
  }
  onSplitterMouseLeave({ source, event }) {
    this.getOtherSplitter(source).element.classList.remove("b-hover");
  }
  onSplitterDrag({ source, event }) {
    if (!event.handled) {
      event.handled = true;
      this.getOtherSplitter(source).onMouseMove(event);
    }
  }
  onSplitterDrop({ source, event }) {
    if (!event.handled) {
      event.handled = true;
      this.getOtherSplitter(source).onMouseUp(event);
    }
  }
  //endregion
  //region Relaying property changes & events
  // Relay relevant config changes to other splits
  afterConfigChange({ name, value: value2 }) {
    if (this.isSplit && this.relayProperties[name]) {
      this.syncSplits((split2) => {
        split2[name] = value2;
      });
    }
  }
  // Sync listeners added at runtime to other splits
  afterAddListener(eventName, listener) {
    if (this.isSplit && !listener.$internal && !ignoreListeners[eventName]) {
      this.syncSplits((split2) => split2.on(eventName, listener));
    }
  }
  afterRemoveListener(eventName, listener) {
    if (!listener.$internal) {
      this.syncSplits((split2) => split2.un(eventName, listener));
    }
  }
  //endregion
  //region Util
  // Call a fn for all splits except the on this fn is called on
  forEachOther(fn2) {
    var _a5;
    const original = this.client.splitFrom || this.client, myName = StringHelper.uncapitalize(this.constructor.$name);
    if (((_a5 = original.features[myName]) == null ? void 0 : _a5.enabled) && !original.inForEachOther) {
      original.inForEachOther = true;
      for (const view of original.subViews) {
        if (view !== this.client) {
          fn2(view);
        }
      }
      original.inForEachOther = false;
    }
  }
  syncSplits(fn2) {
    this.forEachOther(fn2);
  }
  //endregion
};
_ignoreColumnChanges = new WeakMap();
__publicField(Split, "$name", "Split");
__publicField(Split, "featureClass", "");
__publicField(Split, "configurable", {
  /**
   * An array of sub-views. The first sub-view is the original grid, and the others are clones of the original.
   * See the "Accessing a sub-view" section above for more information.
   *
   * ```javascript
   * await grid.split('vertical');
   * const bottom = grid.subViews[1];
   * await bottom.scrollRowIntoView(100);
   * ```
   *
   * Note that this property is accessible directly on the grid instance.
   *
   * @member {Grid.view.Grid[]} subViews
   * @on-owner
   * @readonly
   * @category Common
   */
  subViews: [],
  // Not a config, but still defined in configurable to allow assigning it in pluginConfig,
  /**
   * Properties whose changes should be relayed to sub-views at runtime.
   *
   * Supply an object with property names as keys, and a truthy value to relay the change, or a falsy value to not
   * relay it. The object will be merged with the default values.
   *
   * By default, these properties are relayed:
   * * {@link Grid/view/Grid#property-readOnly}
   * * {@link Grid/view/Grid#property-rowHeight}
   *
   * Example of supplying a custom set of properties to relay:
   * ```javascript
   * const grid = new Grid({
   *     features : {
   *         split : {
   *             relayProperties : {
   *                 readOnly : false, // Do not relay readOnly changes
   *                 myConfig : true   // Relay changes to the myConfig property
   *             }
   *         }
   *     }
   * }
   * ```
   * @config {Object<String,Boolean>}
   */
  relayProperties: {
    value: {
      readOnly: 1,
      rowHeight: 1
    },
    $config: {
      merge: "merge"
    }
  }
});
Split._$name = "Split";
GridFeatureManager.registerFeature(Split, false);

// ../Grid/lib/Grid/feature/LockRows.js
var LockRows = class extends Split {
  // There's no merging of pluginConfigs
  static get pluginConfig() {
    const config = {};
    for (const prop in super.pluginConfig) {
      config[prop] = [...super.pluginConfig[prop]];
    }
    config.chain.push("selectAll", "deselectAll", "afterRenderRow", "afterPluginConfigChange");
    if (!config.override) {
      config.override = [];
    }
    config.override.push("isAllRecordsSelected");
    return config;
  }
  /**
   * Locking rows leads to two chained stores that holds a subset of the records, one for the locked records and one
   * for the rest. This property holds the original store, if you need to access it.
   *
   * ```javascript
   * // With 100 rows, 5 of which are locked
   * console.log(grid.store.count); // 5
   * console.log(grid.originalStore.count); // 100
   * ```
   *
   * @member {Core.data.Store} originalStore
   * @on-owner
   */
  construct() {
    super.construct(...arguments);
    if (this.enabled) {
      this.refreshSuspended = true;
      this.client.suspendRefresh();
    }
  }
  onInternalPaint({ firstPaint }) {
    if (firstPaint) {
      if (this.refreshSuspended) {
        this.client.resumeRefresh();
      }
      this.lockRows();
    }
  }
  async lockRows(options = { fieldName: this.fieldName, filterFn: this.filterFn }) {
    var _a5;
    const me = this, { client } = me;
    if (client.splitFrom || me.disabled || me.$isLocking) {
      return;
    }
    me.$isLocking = true;
    if (me.isSplit) {
      await me.unsplit(true);
    }
    options = me.processOptions(options);
    const {
      store,
      scrollable
    } = client, { fieldName } = options, filterId = `${client.id}-sticky-record-filter`, resetGrid = ObjectHelper.copyProperties({}, client, [
      "flex",
      "minHeight",
      "maxHeight",
      "autoHeight",
      "hideHorizontalScrollbar"
    ]), splitContainer = me.createSplitContainer(options);
    let { filterFn } = options;
    if (resetGrid.maxHeight === 0) {
      resetGrid.maxHeight = null;
    }
    resetGrid.height = client._lastHeight || null;
    me.restorers.push(() => {
      client.setConfig(resetGrid);
      store.removeFilter(filterId);
      client.element.classList.remove("b-locked-rows");
      client.store = client.store.$master;
      client.isLockedRows = client.originalStore = null;
      client.isLockedRowsView = null;
    });
    if (fieldName && !store.modelClass.fieldMap[fieldName]) {
      store.modelClass.addField({
        name: fieldName,
        type: "boolean"
      });
    }
    client.setConfig({
      height: null,
      flex: "0 0 auto",
      minHeight: 0,
      maxHeight: options.maxHeight || "50%",
      autoHeight: true,
      hideHorizontalScrollbar: true,
      hideFooters: true,
      footer: false,
      bbar: null
    });
    client.element.classList.add("b-locked-rows");
    const chainedFields = fieldName ? [fieldName] : "*";
    filterFn = filterFn || ((record) => record[fieldName]);
    const [clone] = me.widgets = [
      me.cloneClient(splitContainer, 0, options, ObjectHelper.merge({
        flex: "1 1 0%",
        hideHeaders: true,
        // Column headers
        header: false,
        usesLockRows: true
      }, me.bottomGridConfig))
    ];
    clone.element.classList.add("b-locked-rows-clone");
    me.subViews = [clone, client];
    client.originalStore = store;
    client.isLockedRows = true;
    client.isLockedRowsView = clone.isLockedRowsView = true;
    client.store = store.chain((record) => filterFn(record), chainedFields, {
      chainFilters: true
    });
    clone.store = store.chain((record) => !filterFn(record), chainedFields, {
      chainFilters: true
    });
    if (DomHelper.scrollBarWidth && scrollable.hasOverflowY) {
      scrollable.overflowY = "scroll";
    }
    !client.scrollable.isPartneredWith(clone.scrollable) && client.scrollable.addPartner(clone.scrollable, {
      x: true
    });
    clone.columns.leaves.forEach((col) => {
      if (col.isRowNumberColumn) {
        clone.refreshColumn(col);
      }
    });
    me._splitOptions = options;
    const bounds = Rectangle.from(client.element);
    client.onInternalResize(client.element, bounds.width, bounds.height);
    client.eachSubGrid((subGrid) => {
      const subGridBounds = Rectangle.from(subGrid.element);
      subGrid.onInternalResize(subGrid.element, subGridBounds.width, subGridBounds.height);
    });
    if ((_a5 = client.features.columnAutoWidth) == null ? void 0 : _a5.enabled) {
      client.features.columnAutoWidth.syncAutoWidthColumns();
    }
    client.scrollable.x += 0.5;
    client.scrollable.y += 0.5;
    me.startSyncingColumns();
    me.$isLocking = false;
    client.trigger("lockRows", { clone });
    return me.subViews;
  }
  processOptions(options) {
    const { fieldName } = options;
    options.direction = "horizontal";
    if (!options.filterFn && !fieldName) {
      throw new Error("LockRows needs `fieldName` or `filterFn`");
    }
    if (typeof options.filterFn === "string") {
      options.filterFn = this.resolveCallback(options.filterFn);
    }
    return options;
  }
  get numberOfLockedRows() {
    return this.isSplit ? this.subViews[1].store.count : 0;
  }
  afterPluginConfigChange({ plugin, applied, origPassedValue, name }) {
    var _a5;
    const me = this;
    if (me.enabled && ((_a5 = me.subViews) == null ? void 0 : _a5[0]) && plugin !== me && applied) {
      const featureName = StringHelper.uncapitalize(plugin.$$name), feature = me.subViews[0].features[featureName];
      if ((feature == null ? void 0 : feature.enabled) && (typeof origPassedValue in { string: 1, boolean: 1, number: 1 } || origPassedValue instanceof Date)) {
        feature[name] = origPassedValue;
      }
    }
  }
  //region Syncing selection
  // Overrides Grids default
  isAllRecordsSelected() {
    const otherGrid = this.isSplit && this.subViews[0];
    return this.overridden.isAllRecordsSelected() && (!otherGrid || otherGrid.isAllRecordsSelected());
  }
  // Runs after Grids default
  selectAll(silent) {
    if (this.isSplit) {
      this.subViews[0].selectAll(silent);
    }
  }
  // Runs after Grids default
  deselectAll(removeCurrentRecordsOnly, silent) {
    if (this.isSplit) {
      this.subViews[0].deselectAll(removeCurrentRecordsOnly, silent);
    }
  }
  afterRenderRow({ row, recordIndex }) {
    if (this.isSplit) {
      row.toggleCls("b-last", recordIndex === this.client.store.count - 1);
    }
  }
  //endregion
  //region Context menu
  populateCellMenu({ record, items: items2 }) {
    var _a5, _b2;
    const me = this, feature = ((_a5 = me.client.splitFrom) == null ? void 0 : _a5.features.lockRows) || me, fieldName = (_b2 = feature._splitOptions) == null ? void 0 : _b2.fieldName;
    if (!me.disabled && fieldName && !feature.filterFn && !me.client.readOnly && !record.isSpecialRow) {
      items2.toggleLocked = {
        text: record[fieldName] ? "L{unlockRow}" : "L{lockRow}",
        localeClass: me,
        icon: `b-icon ${record[fieldName] ? "b-icon-unlock-row" : "b-icon-lock-row"}`,
        weight: 500,
        separator: true,
        closeParent: true,
        onItem: () => {
          record[fieldName] = !record[fieldName];
        }
      };
    }
  }
  //endregion
  async unsplit(silent = false) {
    const me = this, clone = me.subViews[0];
    me.client.trigger("beforeUnlockRows", { clone });
    await super.unsplit(silent);
    !me.isDestroyed && me.client.trigger("unlockRows", { clone });
  }
  doDisable(disable) {
    if (!this.isConfiguring) {
      if (disable) {
        this.unsplit();
      } else {
        this.lockRows();
      }
    }
  }
  //endregion
};
__publicField(LockRows, "$name", "LockRows");
__publicField(LockRows, "configurable", {
  /**
   * The field name whose truthy/falsy state decides whether a record is locked at the top of the grid.
   *
   * Is overridden if a {@link #config-filterFn} is provided.
   *
   * @config {String} fieldName
   * @default 'fixed'
   */
  fieldName: "fixed",
  /**
   * A function, or the name of a function in the Grid's ownership hierarchy, that decides whether a record is
   * locked at the top of the grid.
   *
   * Overrides the {@link #config-fieldName} property.
   *
   * @config {Function|String} filterFn
   * @param {Core.data.Model} record The record to decide upon.
   * @returns {Boolean} Return `true` to lock the record at the top.
   */
  filterFn: null,
  /**
   * An object containing grid configuration for the bottom grid instance.
   *
   * Configuring the bottom grid instance:
   *
   * {@frameworktabs}
   * {@js}
   * ```javascript
   * new Grid({
   *     features : {
   *        lockRows : {
   *            bottomGridConfig : {
   *                hideHeaders : false,
   *                features : {
   *                    filterBar : true
   *                }
   *            }
   *        }
   *    }
   * });
   * ```
   * {@endjs}
   * {@react}
   * ```javascript
   * const App = props => {
   *     const lockRowsFeature : {
   *         bottomGridConfig : {
   *             hideHeaders : false,
   *             features : {
   *                 filterBar : true
   *             }
   *         }
   *     }
   *
   *     return <bryntum-grid lockRowsFeature={lockRowsFeature} />
   * }
   * ```
   * {@endreact}
   * {@vue}
   * ```html
   * <bryntum-grid :lock-rows-feature="lockRowsFeature" />
   * ```
   * ```javascript
   * export default {
   *     setup() {
   *         return {
   *             lockRowsFeature : {
   *                  bottomGridConfig : {
   *                      hideHeaders : false,
   *                      features : {
   *                          filterBar : true
   *                      }
   *                  }
   *              }
   *         };
   *     }
   * }
   * ```
   * {@endvue}
   * {@angular}
   * ```html
   * <bryntum-grid [lockRowsFeature]="lockRowsFeature"></bryntum-grid>
   * ```
   * ```typescript
   * export class AppComponent {
   *      lockRowsFeature : {
   *          bottomGridConfig : {
   *              hideHeaders : false,
   *              features : {
   *                  filterBar : true
   *              }
   *          }
   *      }
   *  }
   * ```
   * {@endangular}
   * {@endframeworktabs}
   *
   * @config {GridConfig}
   * @category Misc
   */
  bottomGridConfig: null
});
LockRows._$name = "LockRows";
GridFeatureManager.registerFeature(LockRows, false);

// ../Grid/lib/Grid/feature/HeaderMenu.js
var HeaderMenu = class extends ContextMenuBase {
  static get configurable() {
    return {
      type: "header",
      /**
       * This is a preconfigured set of items used to create the default context menu.
       *
       * The `items` provided by this feature are listed in the intro section of this class. You can
       * configure existing items by passing a configuration object to the keyed items.
       *
       * To remove existing items, set corresponding keys to `null`:
       *
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         headerMenu : {
       *             items : {
       *                 filter        : null,
       *                 columnPicker  : null
       *             }
       *         }
       *     }
       * });
       * ```
       *
       * See the feature config in the above example for details.
       *
       * @config {Object<String,MenuItemConfig|Boolean|null>} items
       */
      items: null,
      /**
       * Configure as `true` to show two extra menu options to move the selected column to either
       * before its previous sibling, or after its next sibling.
       *
       * This is a keyboard-accessible version of drag/drop column reordering.
       * @config {Boolean}
       * @category Accessibility
       */
      moveColumns: null
      /**
       * See {@link #keyboard-shortcuts Keyboard shortcuts} for details
       * @config {Object<String,KeyMapConfig>} keyMap
       */
    };
  }
  static get defaultConfig() {
    return {
      /**
       * A function called before displaying the menu that allows manipulations of its items.
       * Returning `false` from this function prevents the menu being shown.
       *
       * ```javascript
       *   features         : {
       *       headerMenu : {
       *           processItems({ column, items }) {
       *               // Add or hide existing items here as needed
       *               items.myAction = {
       *                   text   : 'Cool action',
       *                   icon   : 'b-fa b-fa-fw b-fa-ban',
       *                   onItem : () => console.log('Some coolness'),
       *                   weight : 300 // Move to end
       *               };
       *
       *               // Hide column picker
       *               items.columnPicker.hidden = true;
       *           }
       *       }
       *   },
       * ```
       *
       * @config {Function}
       * @param {Object} context An object with information about the menu being shown.
       * @param {Grid.feature.HeaderMenu} context.feature A reference to this feature.
       * @param {Event} context.domEvent The initiating event.
       * @param {Event} context.event DEPRECATED: The initiating event.
       * @param {Number[]} context.point The client `X` and `Y` position of the initiating event.
       * @param {HTMLElement} context.targetElement The target to which the menu is being applied.
       * @param {Grid.column.Column} context.column The current column.
       * @param {Object<String,MenuItemConfig|Boolean>} context.items An object containing the
       *   {@link Core.widget.MenuItem menu item} configs keyed by their id.
       * @returns {Boolean|null} Returning `false` from this function prevents the menu being shown.
       * @preventable
       */
      processItems: null
    };
  }
  static get pluginConfig() {
    const config = super.pluginConfig;
    config.chain.push("populateHeaderMenu");
    return config;
  }
  //endregion
  //region Events
  /**
   * This event fires on the owning Grid before the context menu is shown for a header.
   * Allows manipulation of the items to show in the same way as in the {@link #config-processItems}.
   *
   * Returning `false` from a listener prevents the menu from being shown.
   *
   * @event headerMenuBeforeShow
   * @on-owner
   * @preventable
   * @param {Grid.view.Grid} source The grid
   * @param {Core.widget.Menu} menu The menu
   * @param {Object<String,MenuItemConfig>} items Menu item configs
   * @param {Grid.column.Column} column Column
   */
  /**
   * This event fires on the owning Grid after the context menu is shown for a header
   * @event headerMenuShow
   * @on-owner
   * @param {Grid.view.Grid} source The grid
   * @param {Core.widget.Menu} menu The menu
   * @param {Object<String,MenuItemConfig>} items Menu item configs
   * @param {Grid.column.Column} column Column
   */
  /**
   * This event fires on the owning Grid when an item is selected in the header context menu.
   * @event headerMenuItem
   * @on-owner
   * @param {Grid.view.Grid} source The grid
   * @param {Core.widget.Menu} menu The menu
   * @param {Core.widget.MenuItem} item Selected menu item
   * @param {Grid.column.Column} column Column
   */
  /**
   * This event fires on the owning Grid when a check item is toggled in the header context menu.
   * @event headerMenuToggleItem
   * @on-owner
   * @param {Grid.view.Grid} source The grid
   * @param {Core.widget.Menu} menu The menu
   * @param {Core.widget.MenuItem} item Selected menu item
   * @param {Grid.column.Column} column Column
   * @param {Boolean} checked Checked or not
   */
  //endregion
  //region Menu handlers
  shouldShowMenu(eventParams) {
    const { column } = eventParams;
    return column && column.enableHeaderContextMenu !== false && column !== this.client.timeAxisColumn;
  }
  getDataFromEvent(event) {
    return ObjectHelper.assign(super.getDataFromEvent(event), this.client.getHeaderDataFromEvent(event));
  }
  getTargetElementFromEvent(event) {
    if (event.target.matches(".b-sort-icon")) {
      return event.target.parentElement;
    }
    return event.target;
  }
  populateHeaderMenu({ items: items2, column }) {
    const me = this;
    if (column) {
      if (column.headerMenuItems) {
        ObjectHelper.merge(items2, column.headerMenuItems);
      }
      if (column.isCollapsible) {
        const { collapsed } = column, icon = collapsed ? me.client.rtl ? "left" : "right" : me.client.rtl ? "right" : "left";
        items2.toggleCollapse = {
          weight: 215,
          icon: `b-fw-icon b-icon-collapse-${icon}`,
          text: me.L(collapsed ? "L{expandColumn}" : "L{collapseColumn}"),
          onItem: () => column.collapsed = !collapsed
        };
      }
      if (me.moveColumns) {
        const columnToMoveBefore = me.getColumnToMoveBefore(column), columnToMoveAfter = me.getColumnToMoveAfter(column);
        if (columnToMoveBefore) {
          items2.movePrev = {
            weight: 220,
            icon: "b-fw-icon b-icon-column-move-left",
            text: me.L("L{moveBefore}", StringHelper.encodeHtml(columnToMoveBefore.text)),
            onItem: () => {
              var _a5;
              const { parent: oldParent } = column;
              if (columnToMoveBefore.parent.insertChild(column, columnToMoveBefore)) {
                column.region = columnToMoveBefore.region;
                if (!((_a5 = oldParent.children) == null ? void 0 : _a5.length)) {
                  oldParent.remove();
                }
              }
            }
          };
        }
        if (columnToMoveAfter) {
          items2.moveNext = {
            weight: 230,
            icon: "b-fw-icon b-icon-column-move-right",
            text: me.L("L{moveAfter}", StringHelper.encodeHtml(columnToMoveAfter.text)),
            onItem: () => {
              var _a5;
              const { parent: oldParent } = column;
              if (columnToMoveAfter.parent.insertChild(column, columnToMoveAfter.nextSibling)) {
                column.region = columnToMoveAfter.region;
                if (!((_a5 = oldParent.children) == null ? void 0 : _a5.length)) {
                  oldParent.remove();
                }
              }
            }
          };
        }
      }
    }
    return items2;
  }
  getColumnToMoveBefore(column) {
    const { previousSibling, parent } = column;
    if (previousSibling) {
      return previousSibling.children && !column.children ? previousSibling.children[previousSibling.children.length - 1] : previousSibling;
    }
    if (!parent.isRoot) {
      return parent;
    }
  }
  getColumnToMoveAfter(column) {
    const { nextSibling, parent } = column;
    if (nextSibling) {
      return nextSibling;
    }
    if (!parent.isRoot) {
      return parent;
    }
  }
};
//region Config
__publicField(HeaderMenu, "$name", "HeaderMenu");
HeaderMenu.featureClass = "";
HeaderMenu._$name = "HeaderMenu";
GridFeatureManager.registerFeature(HeaderMenu, true);

// ../Grid/lib/Grid/feature/MergeCells.js
var camelCase = {
  mousedown: "mouseDown",
  mousemove: "mouseMove",
  mouseup: "mouseUp",
  touchdown: "touchDown",
  touchmove: "touchMove",
  touchup: "touchUp",
  pointerover: "mouseOver",
  mouseout: "mouseOut",
  dblclick: "dblClick",
  keydown: "keyDown",
  keypress: "keyPress",
  keyup: "keyUp",
  contextmenu: "contextMenu"
};
var MergeCells = class extends InstancePlugin {
  constructor() {
    super(...arguments);
    // All current ranges (once rendered)
    __publicField(this, "mergedRanges", []);
    // Ranges indexed by row index & column id -> Each range included multiple times, for easy lookup
    __publicField(this, "mergedMap", {});
  }
  static get pluginConfig() {
    return {
      chain: [
        "beforeRenderCell",
        "afterRenderRow",
        "bindStore",
        "afterColumnsChange",
        "afterRemove",
        "afterToggleGroup",
        "afterToggleSubGrid",
        "handleEvent",
        "populateHeaderMenu",
        // 'setHoveredRow'
        "afterSelectionChange"
      ],
      // Grid must perform its update *after* we do.
      before: [
        "onInternalResize",
        "refresh"
      ],
      override: [
        "getColumnFromElement",
        "getRecordFromElement"
      ]
    };
  }
  //endregion
  //region Init
  afterConstruct() {
    const me = this, { client } = me;
    client.eachSubGrid((subGrid) => me.setupSubGrid(subGrid));
    client.rowManager.ion({
      renderDone: "onRenderDone",
      offsetRows: "onOffsetRowsUpdated",
      thisObj: me
    });
    me.bindStore(client.store);
  }
  setupSubGrid(subGrid) {
    subGrid.$mergedCellsElement = DomHelper.createElement({
      parent: subGrid.element,
      className: {
        "b-grid-merged-cells-container": 1
      }
    });
    subGrid.ion({
      beforeInternalResize: "refreshBounds",
      thisObj: this
    });
  }
  doDisable(disable) {
    if (!this.isConfiguring) {
      this.isDisabling = true;
      this.reset();
      this.isDisabling = false;
    }
    super.doDisable(disable);
  }
  updatePassthrough(use) {
    this.client.element.classList.toggle("b-mergecells-passthrough", use);
  }
  updateSortedOnly(sortedOnly) {
    if (!this.isConfiguring) {
      this.reset(true, sortedOnly);
    }
  }
  updateShouldMerge(shouldMerge) {
    if (!this.isConfiguring) {
      this.reset(true, shouldMerge);
    }
  }
  //endregion
  //region Grid hooks
  bindStore(store) {
    this.detachListeners("store");
    store.ion({
      name: "store",
      change: "onStoreChange",
      // Call our refresh logic before grids
      refresh: {
        prio: 1,
        fn: "onStoreRefresh"
      },
      thisObj: this
    });
  }
  // Refresh all ranges when grid is resized
  onInternalResize() {
    this.refreshBounds();
  }
  // This is a chained fn, not meant to be called on this feature. It resets the ranges before the grid is refreshed,
  // to have them recreated during the refresh
  refresh() {
    this.reset(false);
  }
  // Get / create ranges before cell contents are rendered, redirecting the contents to the range
  beforeRenderCell(renderData) {
    const me = this, { column, record, cellElement } = renderData, subGrid = me.client.subGrids[column.region];
    if (!me.disabled && column.mergeCells && (column.isSorted || !me.sortedOnly) && !record.isSpecialRow && !subGrid.collapsed) {
      const mergedRange = me.getMergeRange(record, column);
      if (mergedRange == null ? void 0 : mergedRange.use) {
        cellElement.classList.add("b-merged-cell");
        cellElement.innerHTML = "";
        renderData.cellElement = mergedRange.cellElement;
      }
    }
  }
  afterRenderRow({ row, oldId, oldHeight }) {
    if (oldId === row.id && oldHeight !== row.height) {
      this.heightChanged = true;
    }
  }
  // Refresh all ranges when a column is resized (since it might affect their position and size)
  afterColumnsChange({ action, changes, column }) {
    if (this.disabled) {
      return;
    }
    if (action === "update" && changes.mergeCells && !column.mergeCells) {
      for (const range of this.mergedRanges.slice()) {
        if (range.column === column) {
          this.removeRange(range);
        }
      }
      this.syncDom();
      return;
    }
    this.refreshBounds();
  }
  // Remove might be transitioned, wait until it finishes before resetting (in the afterRemove hook)
  afterRemove() {
    !this.disabled && this.reset();
  }
  // Group collapsed or expanded
  afterToggleGroup() {
    !this.disabled && this.reset();
  }
  // SubGrid collapsed or expanded
  afterToggleSubGrid() {
    !this.disabled && this.reset();
  }
  // setHoveredRow(row) {
  //     this.$hovered = row && this.mergedRanges.find(r => r.fromIndex <= row.dataIndex && r.toIndex >= row.dataIndex);
  //
  //     this.syncDom();
  // }
  // Selection changed, range is considered selected when all of its rows are selected (only in passthrough mode)
  afterSelectionChange() {
    if (!this.passthrough) {
      const { client } = this, indices = client.selectedRecords.map((r) => client.store.indexOf(r));
      let changed = false;
      for (const range of this.mergedRanges) {
        let allSelected = true;
        for (let i = range.fromIndex; i <= range.toIndex && allSelected; i++) {
          allSelected = indices.includes(i);
        }
        if (range.isSelected !== allSelected) {
          range.isSelected = allSelected;
          changed = true;
        }
      }
      changed && this.syncDom();
    }
  }
  //endregion
  //region Grid overrides
  // Extract record from merged cells
  getRecordFromElement(element) {
    var _a5;
    if ((_a5 = element.elementData) == null ? void 0 : _a5.range) {
      return this.client.store.getAt(element.elementData.range.fromIndex);
    }
    return this.overridden.getRecordFromElement(element);
  }
  // Extract column from merged cells
  getColumnFromElement(element) {
    var _a5;
    if ((_a5 = element.elementData) == null ? void 0 : _a5.range) {
      return element.elementData.range.column;
    }
    return this.overridden.getColumnFromElement(element);
  }
  //endregion
  //region Header menu
  // Allow toggling merging cells from the column header menu (unless column is explicitly disallowing it)
  populateHeaderMenu({ column, items: items2 }) {
    if (column.mergeable !== false) {
      items2.mergeCells = {
        text: "L{MergeCells.mergeCells}",
        icon: `b-fw-icon ${column.mergeCells ? "b-icon-checked" : "b-icon-unchecked"}`,
        tooltip: "L{MergeCells.menuTooltip}",
        cls: "b-separator",
        weight: 500,
        disabled: this.disabled,
        onItem: () => column.mergeCells = !column.mergeCells
      };
    }
  }
  //endregion
  //region Relaying events
  // Relay pointer events from the merged cell on grid
  async handleEvent(event) {
    var _a5;
    if (!this.passthrough) {
      const mergedCellsElement = event.target.closest(".b-grid-merged-cells");
      if (mergedCellsElement) {
        const { client } = this, { range } = mergedCellsElement.elementData, { cellEdit } = client.features, { column } = range, type = StringHelper.capitalize((_a5 = camelCase[event.type]) != null ? _a5 : event.type), eventData = {
          grid: client,
          records: [],
          column,
          cellElement: mergedCellsElement.firstElementChild,
          target: event.target,
          event
        };
        for (let i = range.fromIndex; i <= range.toIndex; i++) {
          eventData.records.push(client.store.getAt(i));
        }
        client.trigger(`mergedCell${type}`, eventData);
        if ((cellEdit == null ? void 0 : cellEdit.triggerEvent.toLowerCase()) === `cell${type}`.toLowerCase()) {
          const row = client.rowManager.getRowAt(event.clientY);
          await cellEdit.startEditing({ id: row.id, columnId: column.id });
        } else if (event.type === "click") {
          this.onRangeClick({ range });
        }
      }
    }
  }
  //endregion
  //region Event listeners
  // Select ranges rows when clicking a merged cell
  onRangeClick({ range }) {
    const records = [];
    for (let i = range.fromIndex; i <= range.toIndex; i++) {
      records.push(this.client.store.getAt(i));
    }
    this.client.selectedRecords = records;
  }
  // Reset when data changes
  onStoreChange({ action, removed, added }) {
    !this.disabled && this.reset();
  }
  // Reset before grids refresh listener come into play, grid will redraw
  onStoreRefresh({ source, action }) {
    if (action !== "filter" && action !== "dataset" && !this.disabled) {
      this.reset(Boolean(source.isResourceStore));
    }
  }
  //endregion
  //region Render
  // Element is needed early since it is passed to column renderers, and syncing for each rendered cell would give a
  // lot of overhead. Thus creating element directly instead
  createRangeElement(range) {
    var _a5, _b2, _c2;
    const parent = this.client.getSubGridFromColumn(range.column).$mergedCellsElement, syncId = `${range.fromIndex}-@-${range.column.id}`;
    let element = (_c2 = (_a5 = parent.syncIdMap) == null ? void 0 : _a5[syncId]) != null ? _c2 : (_b2 = parent.releasedIdMap) == null ? void 0 : _b2[syncId];
    if (parent.releasedIdMap) {
      delete parent.releasedIdMap[syncId];
    }
    if (!element) {
      element = DomHelper.createElement(this.createRangeDomConfig(range));
    }
    DomSync.addChild(parent, element, syncId);
    range.element = element;
    range.cellElement = element.firstElementChild;
    return element;
  }
  // Create a DomConfig object for the supplied range, used initially with DomHelper and thereafter with DomSync
  createRangeDomConfig(range) {
    var _a5;
    const { column, fromIndex, toIndex, top, left, right, width, height } = range, { type } = column, record = this.client.store.getAt(fromIndex), domConfig = {
      className: {
        "b-grid-merged-cells": 1,
        // 'b-hover'             : this.$hovered === range,
        "b-selected": range.isSelected
      },
      elementData: {
        range
      },
      style: {
        top,
        left,
        right,
        height,
        width
      },
      dataset: {
        syncId: `${fromIndex}-@-${column.id}`,
        fromIndex,
        toIndex,
        column: column.field,
        columnId: column.id
      },
      children: [
        // Div for an actual cell
        {
          className: new DomClassList(this.client.cellCls).assign({
            [`b-${type == null ? void 0 : type.toLowerCase()}-cell`]: type,
            [column.cellCls]: column.cellCls,
            [column.internalCellCls]: column.internalCellCls
          }).assign(column.autoCls),
          // Don't touch cell children that might have been added by renderer
          keepChildren: true,
          // Tag along the range for easy resolving later
          elementData: {
            range
          },
          // Dataset expected on cells
          dataset: {
            column: column.field,
            columnId: column.id
          },
          // Size using configured row height, to at least have a chance of lining up with surrounding cells
          style: {
            height: this.client.rowManager.rowHeight
          }
        }
      ]
    };
    (_a5 = column.mergedRenderer) == null ? void 0 : _a5.call(column, { domConfig, value: column.getRawValue(record), record, column, fromIndex, toIndex });
    return domConfig;
  }
  buildMergedCellsConfig(fromIndex, toIndex, rows) {
    var _a5, _b2;
    const me = this, { client } = me, { rowManager } = client;
    fromIndex = fromIndex != null ? fromIndex : (_a5 = rowManager.topRow) == null ? void 0 : _a5.dataIndex;
    toIndex = toIndex != null ? toIndex : (_b2 = rowManager.bottomRow) == null ? void 0 : _b2.dataIndex;
    const domConfigMap = client.regions.reduce((map2, region) => {
      map2[region] = {
        className: {
          "b-grid-merged-cells-container": 1
        },
        children: []
      };
      return map2;
    }, {});
    for (const range of me.mergedRanges) {
      if (range.use && range.fromIndex <= toIndex && range.toIndex >= fromIndex) {
        me.updateRange(range, me.heightChanged, rows);
        domConfigMap[range.column.region].children.push(me.createRangeDomConfig(range));
      }
    }
    return domConfigMap;
  }
  syncDom(force = false) {
    const me = this, { client } = me, { rowManager } = client;
    if (!force && !rowManager.topRow || me.disabled && !me.isDisabling) {
      return;
    }
    const domConfigMap = this.buildMergedCellsConfig();
    client.eachSubGrid((subGrid) => {
      DomSync.sync({
        targetElement: subGrid.$mergedCellsElement,
        syncIdField: "syncId",
        domConfig: domConfigMap[subGrid.region]
      });
    });
    me.heightChanged = false;
  }
  // Called after rows are updated, sync visible ranges per subgrid
  onRenderDone() {
    this.syncDom();
  }
  // Reset when offset rows is being updated
  onOffsetRowsUpdated() {
    this.reset(true);
  }
  //endregion
  //region Ranges
  // Remove range from list and index/column map, leaving no trace it since we don't know if it will ever
  // come back (element will be released by DomSync)
  removeRange(range) {
    ArrayHelper.remove(this.mergedRanges, range);
    for (let index = range.fromIndex; index <= range.toIndex; index++) {
      delete this.mergedMap[`${index}-@-${range.column.id}`];
    }
  }
  // Full reset, regenerating all ranges
  reset(redraw = true, force = false) {
    const me = this, columns = me.client.columns.visibleColumns.filter((c) => c.mergeCells && (c.isSorted || !me.sortedOnly || force), true), hadRanges = me.mergedRanges.length;
    me.mergedRanges = [];
    me.mergedMap = {};
    if (redraw && !me.client.refreshSuspended) {
      for (const row of me.client.rowManager.rows) {
        for (const column of columns) {
          row.renderCell(row.getCell(column.id));
        }
      }
      me.syncDom(hadRanges && !me.client.rowManager.rowCount);
    }
  }
  // Refreshes existing ranges coords
  refreshBounds() {
    var _a5;
    const { mergedRanges } = this, row = this.client.rowManager.topRow, boundsMap = /* @__PURE__ */ new Map(), isRtl = this.client.rtl;
    for (const range of mergedRanges.slice()) {
      const { column } = range, cellElement = row.getCell(column.id);
      if (cellElement) {
        let { rowWidth, bounds } = (_a5 = boundsMap.get(column)) != null ? _a5 : {};
        if (!bounds) {
          rowWidth = cellElement.parentElement.offsetWidth;
          bounds = Rectangle.from(cellElement, cellElement.parentElement);
          boundsMap.set(column, { rowWidth, bounds });
        }
        if (isRtl) {
          range.right = rowWidth - bounds.right;
        } else {
          range.left = bounds.left;
        }
        range.width = bounds.width;
      } else {
        this.removeRange(range);
      }
    }
    this.syncDom();
  }
  // Updates the range as user scrolls, until its start and end coords are fully known
  // Rows argument is required to render merged cells on export. We provide row-like objects there
  updateRange(range, force, rows = null) {
    const { store, rowManager } = this.client, { topRendered, bottomRendered } = range, { rowOffsetHeight } = rowManager;
    let { fromIndex, toIndex } = range, topRow, bottomRow;
    force = rows ? rows.length > 0 : force;
    if (!force && topRendered && bottomRendered) {
      return;
    }
    if (!topRendered || force) {
      if (rows) {
        const index = rows.findIndex((row) => row.dataIndex >= fromIndex);
        topRow = rows[index];
        fromIndex = topRow.dataIndex;
      } else {
        do {
          topRow = rowManager.getRowById(store.getAt(fromIndex));
        } while (!topRow && fromIndex++ < toIndex);
      }
      range.topRendered = fromIndex === range.fromIndex;
      const rowsAbove = fromIndex - range.fromIndex;
      range.top = topRow.top - rowsAbove * (rows ? topRow.offsetHeight : rowOffsetHeight);
    }
    if (!bottomRendered || force) {
      if (rows) {
        const index = rows.findIndex((row) => row.dataIndex === toIndex);
        bottomRow = rows[index === -1 ? rows.length - 1 : index];
        fromIndex = bottomRow.dataIndex;
      } else {
        do {
          bottomRow = rowManager.getRowById(store.getAt(toIndex));
        } while (!bottomRow && toIndex-- > fromIndex);
      }
      range.bottomRendered = toIndex === range.toIndex;
      const rowsBelow = range.toIndex - toIndex;
      range.bottom = bottomRow.bottom + rowsBelow * (rows ? bottomRow.offsetHeight : rowOffsetHeight);
    }
    range.height = range.bottom - range.top;
  }
  // Retrieve a range for the specified record / column, creating a new one if none found by walking upwards and
  // downwards until a deviating value is found
  getMergeRange(record, column) {
    if (record.isSpecialRow) {
      return;
    }
    const me = this, { mergedMap } = me, { store } = me.client, row = me.client.rowManager.topRow, columnId = column.id, index = store.indexOf(record), key = `${index}-@-${columnId}`, isRtl = this.client.rtl, value2 = column.getRawValue(record);
    let range = mergedMap[key];
    if (!range) {
      range = mergedMap[key] = {
        column
      };
      me.mergedRanges.push(range);
      let earlierRecord, earlierValue, earlierIndex = index - 1;
      do {
        earlierRecord = store.getAt(earlierIndex);
        earlierValue = earlierRecord && column.getRawValue(earlierRecord);
        if (mergedMap[`${earlierIndex}-@-${columnId}`]) {
          earlierRecord = null;
        }
        earlierIndex--;
      } while (earlierRecord && !earlierRecord.isSpecialRow && earlierValue === value2);
      let laterRecord, laterValue, laterIndex = index + 1;
      do {
        laterRecord = store.getAt(laterIndex);
        laterValue = laterRecord && column.getRawValue(laterRecord);
        if (mergedMap[`${laterIndex}-@-${columnId}`]) {
          laterRecord = null;
        }
        laterIndex++;
      } while (laterRecord && !laterRecord.isSpecialRow && laterValue === value2);
      range.fromIndex = earlierIndex + 2;
      range.toIndex = laterIndex - 2;
      if (me.shouldMerge && range.toIndex - range.fromIndex > 0) {
        for (let i = range.fromIndex + 1; i <= range.toIndex; i++) {
          const currentRecord = store.getAt(i), previousRecord = store.getAt(i - 1);
          if (me.shouldMerge({ column, record: currentRecord, previousRecord, value: value2 }) === false) {
            range.toIndex = i - 1;
            break;
          }
        }
      }
      if (range.toIndex - range.fromIndex > 0) {
        for (let i = range.fromIndex; i <= range.toIndex; i++) {
          mergedMap[`${i}-@-${columnId}`] = range;
        }
        const cellElement = row.getCell(columnId), rowWidth = cellElement.parentElement.offsetWidth, cellBounds = Rectangle.from(cellElement, cellElement.parentElement);
        if (isRtl) {
          range.right = rowWidth - cellBounds.right;
        } else {
          range.left = cellBounds.left;
        }
        range.width = cellBounds.width;
        range.use = true;
      }
    }
    if ((range == null ? void 0 : range.use) && !range.element) {
      me.createRangeElement(range);
    }
    return range;
  }
  //endregion
};
//region Config
__publicField(MergeCells, "$name", "MergeCells");
__publicField(MergeCells, "configurable", {
  /**
   * By default, merged cells allow pointer events to pass through to the underlying row/cell, to allow selecting
   * a row and editing an individual cell even when they are merged. Configure as `false` to allow merged cells to
   * catch and react to the pointer events instead.
   *
   * ```javascript
   * const grid = new Grid({
   *     features : {
   *         mergeCells : {
   *             // Let merged cells react to pointer events
   *             passthrough : false
   *         }
   *     }
   * });
   * ```
   *
   * @prp {Boolean}
   */
  passthrough: true,
  /**
   * Configure as `false` to allow merging cells in columns that are not sorted.
   *
   * {@note}
   * Note that this will have a slight negative impact on performance, since cells in all columns configured to
   * merge cells have to be iterated.
   * {/@note}
   *
   * @prp {Boolean}
   * @default true
   */
  sortedOnly: true,
  /**
   * Hook used to control which cells should be included in a merged range of cells.
   *
   * The feature first determines the range using its default logic. It then calls this hook for each cell in the
   * range except the first, and if the hook returns `false`, the cell is not included in the range but instead a
   * new range is started. The hook thus controls if a cell should be merged with the cell above it or not.
   *
   * Example usage:
   *
   * ```javascript
   * const grid = new Grid({
   *    features : {
   *        mergeCells : {
   *            shouldMerge({ column, record, previousRecord, value }) {
   *                // Only merge cells in the "Age" column as long as the "Name" matches the previous record
   *                if (column.field === 'age') {
   *                    return record.name === previousRecord.name;
   *                }
   *
   *                // Merge other cells as usual
   *                return true;
   *            }
   *        }
   *    }
   * });
   * ```
   *
   * @prp {Function}
   * @param {Object} context
   * @param {Grid.column.Column} context.column Current column (readonly)
   * @param {Core.data.Model} context.record Current record (readonly)
   * @param {Core.data.Model} context.previousRecord Previous record (above current record) (readonly)
   * @param {*} context.value Cell's raw value (readonly)
   * @returns {Boolean} Return `true` to merge with the cell above, `false` to start a new range
   */
  shouldMerge: null
});
MergeCells._$name = "MergeCells";
GridFeatureManager.registerFeature(MergeCells);

// ../Grid/lib/Grid/feature/QuickFind.js
var actions2 = {
  goToNextHit: 1,
  goToPrevHit: 1,
  showFilterEditor: 1,
  clearSearch: 1
};
var QuickFind = class extends InstancePlugin {
  // Plugin configuration. This plugin chains some of the functions in Grid.
  static get pluginConfig() {
    return {
      chain: ["onElementKeyPress", "onCellNavigate"]
    };
  }
  //endregion
  //region Init
  static get properties() {
    return {
      hitCls: "b-quick-hit",
      hitCellCls: "b-quick-hit-cell",
      hitCellBadgeCls: "b-quick-hit-cell-badge",
      hitTextCls: "b-quick-hit-text"
    };
  }
  construct(grid, config) {
    super.construct(grid, config);
    Object.assign(this, {
      grid,
      treeWalker: grid.setupTreeWalker(grid.element, DomHelper.NodeFilter.SHOW_TEXT, () => DomHelper.NodeFilter.FILTER_ACCEPT)
    });
  }
  isActionAvailable({ actionName }) {
    if (actions2[actionName]) {
      const { focusedCell } = this.grid;
      return !this.disabled && (focusedCell == null ? void 0 : focusedCell.record) && !focusedCell.isActionable && this.find.length > 0;
    }
  }
  doDisable(disable) {
    if (disable) {
      this.clear();
    }
    super.doDisable(disable);
  }
  get store() {
    return this.grid.store;
  }
  //endregion
  //region Show/hide QuickFind
  /**
   * Shows a "searchfield" in the header. Triggered automatically when you have a cell focused and start typing.
   * @private
   */
  showQuickFind() {
    const me = this, { grid } = me, header = grid.getHeaderElement(me.columnId);
    if (header) {
      if (!me.headerField) {
        const [element, field, badge] = DomHelper.createElement({
          tag: "div",
          className: "b-quick-hit-header",
          children: [
            { tag: "div", className: "b-quick-hit-field" },
            { tag: "div", className: "b-quick-hit-badge" }
          ]
        }, { returnAll: true });
        if (me.mode === "header") {
          header.appendChild(element);
        } else {
          element.className += " b-quick-hit-mode-grid";
          grid.element.appendChild(element);
        }
        me.headerField = {
          header: element,
          field,
          badge,
          colHeader: header
        };
      }
      me.headerField.field.innerHTML = me.find;
      me.headerField.badge.innerHTML = me.found.length;
      header.classList.add("b-quick-find-header");
    }
    if ((header || grid.hideHeaders) && !me.renderListenerInitialized) {
      grid.rowManager.ion({
        rendercell: me.renderCell,
        thisObj: me
      });
      me.renderListenerInitialized = true;
    }
  }
  /**
   * Hide the "searchfield" and remove highlighted hits. Called automatically when pressing [esc] or backspacing away
   * the keywords.
   * @private
   */
  hideQuickFind() {
    var _a5;
    const me = this, { grid, headerField } = me;
    for (const hit of me.prevFound || me.found) {
      const row = grid.getRowById(hit.id);
      if (row) {
        row.forceInnerHTML = true;
        const cellElement = row.getCell(me.columnId);
        cellElement._content = null;
        row.renderCell(cellElement);
        row.forceInnerHTML = false;
      }
    }
    if (headerField) {
      (_a5 = headerField.header.parentNode) == null ? void 0 : _a5.removeChild(headerField.header);
      headerField.colHeader.classList.remove("b-quick-find-header");
      me.headerField = null;
    }
    if (me.renderListenerInitialized) {
      grid.rowManager.un({ rendercell: me.renderCell }, me);
      me.renderListenerInitialized = false;
    }
    grid.trigger("hideQuickFind");
  }
  //endregion
  //region Search
  /**
   * Performs a search and highlights hits. If find is empty, QuickFind is closed.
   * @param {String} find Text to search for
   * @param {String} columnFieldOrId Column to search
   */
  search(find, columnFieldOrId = this.columnId, fromSplit = false) {
    var _a5;
    const me = this, { grid } = me, column = grid.columns.getById(columnFieldOrId) || grid.columns.get(columnFieldOrId), found = me.store.findByField(column.field, find, column.mergeCells && column.isSorted);
    let i = 1;
    Object.assign(me, {
      foundMap: {},
      prevFound: me.found,
      found,
      find,
      columnId: column.id,
      findRe: new RegExp(`(\\s+)?(${StringHelper.escapeRegExp(String(find))})(\\s+)?`, "ig")
    });
    if (find) {
      me.showQuickFind();
    } else {
      me.hideQuickFind();
    }
    for (const cellElement of DomHelper.children(grid.element, `.${me.hitCls}`)) {
      cellElement.classList.remove(me.hitCls, me.hitCellCls);
      if (cellElement._originalContent) {
        cellElement.innerHTML = cellElement._originalContent;
        cellElement._originalContent = null;
      }
    }
    if (!found) {
      return;
    }
    if (found.length > 0 && !fromSplit) {
      me.gotoClosestHit(grid.focusedCell, found);
    }
    for (const hit of found) {
      me.foundMap[hit.id] = i++;
      const row = grid.getRowById(hit.data.id);
      row == null ? void 0 : row.renderCell(row.getCell(column.id));
      if (i > 1e3) {
        break;
      }
    }
    (_a5 = grid.syncSplits) == null ? void 0 : _a5.call(grid, (other) => other.features.quickFind.search(find, columnFieldOrId, true));
    grid.trigger("quickFind", { find, found });
  }
  /**
   * Clears and closes QuickFind.
   */
  clear() {
    var _a5;
    if (this.find || ((_a5 = this.found) == null ? void 0 : _a5.length)) {
      this.search("");
    }
  }
  /**
   * Number of results found
   * @type {Number}
   * @readonly
   */
  get foundCount() {
    var _a5, _b2;
    return (_b2 = (_a5 = this.found) == null ? void 0 : _a5.length) != null ? _b2 : 0;
  }
  /**
   * Found results (as returned by Store#findByField), an array in format { index: x, data: record }
   * @member {StoreSearchResult[]} found
   * @readonly
   */
  //endregion
  //region Navigation
  /**
   * Go to specified hit.
   * @param {Number} index
   */
  gotoHit(index) {
    const nextHit = this.found[index];
    if (nextHit) {
      this.grid.focusCell({
        columnId: this.columnId,
        id: nextHit.id
      }, { doSelect: true });
    }
    return Boolean(nextHit);
  }
  gotoClosestHit(focusedCell, found) {
    const focusedIndex = focusedCell ? this.grid.store.indexOf(focusedCell.id) : 0, foundSorted = found.slice().sort(
      (a, b) => Math.abs(a.index - focusedIndex) - Math.abs(b.index - focusedIndex)
    );
    this.gotoHit(found.indexOf(foundSorted[0]));
  }
  /**
   * Go to the first hit.
   */
  gotoFirstHit() {
    this.gotoHit(0);
  }
  /**
   * Go to the last hit.
   */
  gotoLastHit() {
    this.gotoHit(this.found.length - 1);
  }
  /**
   * Select the next hit, scrolling it into view. Triggered with [f3] or [ctrl]/[cmd] + [g].
   */
  gotoNextHit() {
    var _a5, _b2, _c2, _d;
    const me = this;
    if (!((_a5 = me.found) == null ? void 0 : _a5.length)) {
      return;
    }
    const { grid } = me, currentId = (_d = (_b2 = grid._focusedCell) == null ? void 0 : _b2.id) != null ? _d : (_c2 = grid.lastFocusedCell) == null ? void 0 : _c2.id, currentIndex = grid.store.indexOf(currentId) || 0, nextHit = me.found.find((hit) => hit.index > currentIndex);
    if (nextHit) {
      grid.focusCell({
        columnId: me.columnId,
        id: nextHit.id
      }, { doSelect: true });
    } else {
      me.gotoFirstHit();
    }
  }
  /**
   * Select the previous hit, scrolling it into view. Triggered with [shift] + [f3] or [shift] + [ctrl]/[cmd] + [g].
   */
  gotoPrevHit() {
    var _a5, _b2, _c2, _d;
    const me = this;
    if (!((_a5 = me.found) == null ? void 0 : _a5.length)) {
      return;
    }
    const { grid, found } = me, currentId = (_d = (_b2 = grid._focusedCell) == null ? void 0 : _b2.id) != null ? _d : (_c2 = grid.lastFocusedCell) == null ? void 0 : _c2.id, currentIndex = grid.store.indexOf(currentId) || 0;
    let prevHit;
    if (!found.length) {
      return;
    }
    for (let i = found.length - 1; i--; i >= 0) {
      if (found[i].index < currentIndex) {
        prevHit = found[i];
        break;
      }
    }
    if (prevHit) {
      grid.focusCell({
        columnId: me.columnId,
        id: prevHit.id
      }, { doSelect: true });
    } else {
      me.gotoLastHit();
    }
  }
  //endregion
  //region Render
  /**
   * Called from SubGrid when a cell is rendered.
   * @private
   */
  renderCell({ cellElement, column, record }) {
    var _a5;
    const me = this, { classList } = cellElement, {
      treeWalker,
      findRe,
      hitTextCls
    } = me, hitIndex = me.columnId === column.id && ((_a5 = me.foundMap) == null ? void 0 : _a5[record.id]);
    if (hitIndex) {
      classList.add(me.hitCls);
      cellElement.isQuickHit = true;
      cellElement._originalContent = cellElement.innerHTML;
      const inner = treeWalker.currentNode = DomHelper.down(cellElement, ".b-grid-cell-value,.b-tree-cell-value") || cellElement;
      for (let textNode = treeWalker.nextNode(); textNode && inner.contains(textNode); ) {
        const nodeToReplace = textNode, textContent = textNode.nodeValue, newText = ["<span>"];
        textNode = treeWalker.nextNode();
        let offset = findRe.lastIndex;
        for (let match = findRe.exec(textContent); match; match = findRe.exec(textContent)) {
          const preamble = textContent.substring(offset, match.index), spaceBefore = match[1] ? "\xA0" : "", v = match[2], spaceAfter = match[3] ? "\xA0" : "";
          newText.push(`${StringHelper.encodeHtml(preamble)}${spaceBefore}<span class="${hitTextCls}">${v}</span>${spaceAfter}`);
          offset = findRe.lastIndex;
        }
        newText.push(StringHelper.encodeHtml(textContent.substring(offset)), "</span>");
        nodeToReplace.parentNode.insertBefore(DomHelper.createElementFromTemplate(newText.join(""), {
          fragment: true
        }), nodeToReplace);
        nodeToReplace.remove();
      }
      DomHelper.createElement({
        parent: cellElement,
        className: me.hitCellBadgeCls,
        text: hitIndex
      });
    }
  }
  //endregion
  //region Events
  onBackspace(event) {
    const me = this;
    if (me.find) {
      me.find = me.find.substr(0, me.find.length - 1);
      me.search(me.find);
      return true;
    }
    return false;
  }
  clearSearch() {
    if (this.find) {
      this.find = "";
      this.search(this.find);
      return true;
    }
    return false;
  }
  showFilterEditor() {
    const me = this, { filter: filter2 } = me.client.features;
    if (filter2 && me.columnId && me.foundCount) {
      me.clear();
      filter2.showFilterEditor(me.client.columns.getById(me.columnId), me.find);
    }
  }
  /**
   * Chained function called on grids keypress event. Handles input for "searchfield".
   * @private
   * @param event
   */
  onElementKeyPress(event) {
    var _a5;
    const me = this, { grid } = me, { focusedCell } = grid;
    if (!event.handled && !me.disabled && (focusedCell == null ? void 0 : focusedCell.record) && !focusedCell.isActionable && ((_a5 = event.key) == null ? void 0 : _a5.length) === 1) {
      const column = grid.columns.getById(grid._focusedCell.columnId);
      if (column && column.searchable !== false) {
        me.columnId = grid._focusedCell.columnId;
        me.find += event.key;
        me.search(me.find);
      }
    }
  }
  onCellNavigate(grid, fromCellSelector, toCellSelector) {
    const me = this;
    if (me.find && (!toCellSelector || toCellSelector.columnId !== me.columnId)) {
      me.clear();
    }
  }
  //endregion
};
//region Config
__publicField(QuickFind, "$name", "QuickFind");
__publicField(QuickFind, "configurable", {
  mode: "header",
  find: "",
  /**
   * See {@link #keyboard-shortcuts Keyboard shortcuts} for details
   * @config {Object<String,KeyMapConfig>}
   */
  keyMap: {
    F3: "gotoNextHit",
    "Shift+F3": "gotoPrevHit",
    "Ctrl+g": "gotoNextHit",
    "Ctrl+Shift+g": "gotoPrevHit",
    "Ctrl+Shift+f": "showFilterEditor",
    Escape: "clearSearch",
    //Private
    Backspace: "onBackspace"
  }
});
QuickFind._$name = "QuickFind";
GridFeatureManager.registerFeature(QuickFind);

// ../Grid/lib/Grid/feature/RegionResize.js
var RegionResize = class extends InstancePlugin {
  static get pluginConfig() {
    return {
      chain: ["onElementPointerDown", "onElementDblClick", "onElementTouchMove", "onSubGridCollapse", "onSubGridExpand", "render"]
    };
  }
  //endregion
  updateShowSplitterButtons(value2) {
    this.client.element.classList.toggle("b-hide-splitter-buttons", !value2);
  }
  onElementDblClick(event) {
    const me = this, { client } = me, splitterEl = event.target.closest(".b-grid-splitter-collapsed");
    if (splitterEl && !me.expanding) {
      me.expanding = true;
      let region = splitterEl.dataset.region, subGrid = client.getSubGrid(region);
      if (!subGrid.collapsed) {
        region = client.getLastRegions()[1];
        subGrid = client.getSubGrid(region);
      }
      subGrid.expand().then(() => me.expanding = false);
    }
  }
  //region Move splitter
  /**
   * Begin moving splitter.
   * @private
   * @param splitterElement Splitter element
   * @param {Event} domEvent The initiating DOM event.
   */
  startMove(splitterElement, domEvent) {
    const me = this, { clientX } = domEvent, { client } = me, region = splitterElement.dataset.region, gridEl = client.element, nextRegion = client.regions[client.regions.indexOf(region) + 1], nextSubGrid = client.getSubGrid(nextRegion), splitterSubGrid = client.getSubGrid(region);
    if (splitterElement.classList.contains("b-grid-splitter-collapsed")) {
      return;
    }
    let subGrid = splitterSubGrid, flip = 1;
    if (subGrid.flex != null) {
      if (nextSubGrid.flex == null) {
        subGrid = nextSubGrid;
        flip = -1;
      }
    }
    if (client.rtl) {
      flip *= -1;
    }
    const availableWidth = Object.values(client.subGrids).reduce((sum, subGrid2) => {
      return subGrid2.width + sum;
    }, 0);
    client.trigger("splitterDragStart", { subGrid, domEvent });
    me.dragContext = {
      element: splitterElement,
      headerEl: subGrid.header.element,
      subGridEl: subGrid.element,
      subGrid,
      splitterSubGrid,
      originalWidth: subGrid.element.offsetWidth,
      originalX: clientX,
      minWidth: subGrid.minWidth || 0,
      maxWidth: Math.min(availableWidth, subGrid.maxWidth || availableWidth),
      flip
    };
    gridEl.classList.add("b-moving-splitter");
    splitterSubGrid.toggleSplitterCls("b-moving");
    me.pointerDetacher = EventHelper.on({
      element: document,
      pointermove: "onPointerMove",
      pointerup: "onPointerUp",
      thisObj: me
    });
  }
  /**
   * Stop moving splitter.
   * @param {Event} domEvent The initiating DOM event.
   * @private
   */
  endMove(domEvent) {
    const me = this, { dragContext, client } = me;
    if (dragContext) {
      const { subGrid } = dragContext;
      domEvent.preventDefault();
      me.pointerDetacher();
      client.element.classList.remove("b-moving-splitter");
      dragContext.splitterSubGrid.toggleSplitterCls("b-moving", false);
      me.dragContext = null;
      client.trigger("splitterDragEnd", { subGrid, domEvent });
    }
  }
  onCollapseClick(subGrid, splitterEl, domEvent) {
    const me = this, { client } = me, region = splitterEl.dataset.region, regions = client.getLastRegions();
    if (client.trigger("splitterCollapseClick", { subGrid, domEvent }) === false) {
      return;
    }
    if (regions[0] === region) {
      const lastSubGrid = client.getSubGrid(regions[1]);
      if (lastSubGrid.collapsed) {
        lastSubGrid.expand(me.animateCollapseExpand);
        return;
      }
    }
    subGrid.collapse(me.animateCollapseExpand);
  }
  onExpandClick(subGrid, splitterEl, domEvent) {
    const me = this, { client } = me, region = splitterEl.dataset.region, regions = client.getLastRegions();
    if (client.trigger("splitterExpandClick", { subGrid, domEvent }) === false) {
      return;
    }
    if (regions[0] === region) {
      if (!subGrid.collapsed) {
        const lastSubGrid = client.getSubGrid(regions[1]);
        lastSubGrid.collapse(me.animateCollapseExpand);
        return;
      }
    }
    subGrid.expand(me.animateCollapseExpand);
  }
  /**
   * Update splitter position.
   * @private
   * @param newClientX
   */
  updateMove(newClientX) {
    const { dragContext } = this;
    if (dragContext) {
      const diffX = newClientX - dragContext.originalX, newWidth = Math.max(Math.min(dragContext.maxWidth, dragContext.originalWidth + diffX * dragContext.flip), 0);
      dragContext.subGrid.width = Math.max(newWidth, dragContext.minWidth);
    }
  }
  //endregion
  //region Events
  /**
   * Start moving splitter on mouse down (on splitter).
   * @private
   * @param event
   */
  onElementPointerDown(event) {
    var _a5;
    const me = this, { target } = event, splitter = event.button === 0 && target.closest(":not(.b-row-reordering):not(.b-dragging-event):not(.b-dragging-task):not(.b-dragging-header):not(.b-dragselecting) .b-grid-splitter"), subGrid = splitter && me.client.getSubGrid(splitter.dataset.region);
    let toggle;
    if (splitter) {
      if (target.closest(".b-grid-splitter-button-collapse")) {
        me.onCollapseClick(subGrid, splitter, event);
      } else if (target.closest(".b-grid-splitter-button-expand")) {
        me.onExpandClick(subGrid, splitter, event);
      } else if (me.enableDragging) {
        me.startMove(splitter, event);
        toggle = splitter;
      }
    }
    if (event.pointerType === "touch" || ((_a5 = me.touchedSplitter) == null ? void 0 : _a5.classList.contains("b-touching"))) {
      me.toggleTouchSplitter(toggle);
    }
  }
  /**
   * Move splitter on mouse move.
   * @private
   * @param event
   */
  onPointerMove(event) {
    if (this.dragContext) {
      this.updateMove(event.clientX);
      event.preventDefault();
    }
  }
  onElementTouchMove(event) {
    if (this.dragContext) {
      event.preventDefault();
    }
  }
  /**
   * Stop moving splitter on mouse up.
   * @private
   * @param event
   */
  onPointerUp(event) {
    if (this.enableDragging) {
      this.endMove(event);
    }
  }
  onSubGridCollapse({ subGrid }) {
    const splitterEl = this.client.resolveSplitter(subGrid), regions = this.client.getLastRegions();
    if (regions[1] === subGrid.region) {
      splitterEl.classList.add("b-grid-splitter-allow-collapse");
    }
  }
  onSubGridExpand({ subGrid }) {
    const splitterEl = this.client.resolveSplitter(subGrid);
    splitterEl.classList.remove("b-grid-splitter-allow-collapse");
  }
  //endregion
  /**
   * Adds b-touching CSS class to splitterElements when touched. Removes when touched outside.
   * @private
   * @param splitterElement
   */
  toggleTouchSplitter(splitterElement) {
    const me = this, { touchedSplitter } = me;
    if (splitterElement && touchedSplitter && splitterElement.dataset.region !== touchedSplitter.dataset.region) {
      me.toggleTouchSplitter();
    }
    const splitterSubGrid = me.client.getSubGrid(splitterElement ? splitterElement.dataset.region : touchedSplitter == null ? void 0 : touchedSplitter.dataset.region);
    if (splitterSubGrid) {
      splitterSubGrid.toggleSplitterCls("b-touching", Boolean(splitterElement));
      if (splitterElement) {
        splitterSubGrid.startSplitterButtonSyncing();
      } else {
        splitterSubGrid.stopSplitterButtonSyncing();
      }
    }
    me.touchedSplitter = splitterElement;
  }
  render() {
    const { regions, subGrids } = this.client;
    if (regions.length === 2) {
      const firstSubGrid = subGrids[regions[0]];
      if (firstSubGrid.maxWidth && firstSubGrid.width >= firstSubGrid.maxWidth) {
        firstSubGrid.splitterElement.classList.add("b-left-only");
      }
    } else if (regions.length > 2) {
      subGrids[regions[0]].splitterElement.classList.add("b-left-only");
      subGrids[regions[1]].splitterElement.classList.add("b-right-only");
    }
  }
  updateEnableDragging(value2) {
    this.client.element.classList.toggle("b-grid-splitter-no-drag", !value2);
  }
};
// region Init
__publicField(RegionResize, "$name", "RegionResize");
__publicField(RegionResize, "configurable", {
  /**
   * Set to `false` to hide the splitter's collapse/expand buttons
   * @prp {Boolean}
   * @default
   */
  showSplitterButtons: true,
  /**
   * Set to `false` to not use transitions when expanding or collapsing a sub grid
   * @prp {Boolean}
   * @default true
   */
  animateCollapseExpand: true,
  /**
   * This flag prevents dragging if set to `false` but the collapse / expand buttons will still be functional.
   * @prp {Boolean}
   * @default
   */
  enableDragging: true
});
RegionResize.featureClass = "b-split";
RegionResize._$name = "RegionResize";
GridFeatureManager.registerFeature(RegionResize);

// ../Grid/lib/Grid/feature/RowCopyPaste.js
var isKeyboardEvent = (e) => e instanceof KeyboardEvent || (e == null ? void 0 : e.type) === "keydown" && (e == null ? void 0 : e.target);
var RowCopyPaste = class extends CopyPasteBase {
  constructor() {
    super(...arguments);
    // Used in events to separate events from different features from each other
    __publicField(this, "entityName", "row");
  }
  static get pluginConfig() {
    return {
      assign: [
        "copyRows",
        "pasteRows"
      ],
      chain: [
        "populateCellMenu"
      ]
    };
  }
  construct(grid, config) {
    super.construct(grid, config);
    grid.rowManager.ion({
      beforeRenderRow: "onBeforeRenderRow",
      thisObj: this
    });
    this.grid = grid;
  }
  onBeforeRenderRow({ row, record }) {
    var _a5;
    row.cls["b-cut-row"] = this.isCut && ((_a5 = this.cutData) == null ? void 0 : _a5.includes(record));
  }
  isActionAvailable({ actionName, event }) {
    var _a5;
    if (!super.isActionAvailable(...arguments)) {
      return false;
    }
    const { grid } = this, { target } = event;
    if ((actionName === "cut" || actionName === "paste") && grid.isLockedRows) {
      return false;
    }
    return (actionName === "paste" || ((_a5 = grid.selectedRecords) == null ? void 0 : _a5.length) > 0) && // No copy/cut action when no selected records
    (!target || Boolean(target.closest(".b-grid-body-container")));
  }
  async copy(record) {
    await this.copyRows(false, record);
  }
  async cut(record) {
    await this.copyRows(true, record);
  }
  paste(referenceRecord) {
    if (isKeyboardEvent(referenceRecord)) {
      referenceRecord = this.client.getRecordFromElement(referenceRecord.target);
    }
    return this.pasteRows((referenceRecord == null ? void 0 : referenceRecord.isModel) ? referenceRecord : null);
  }
  /**
   * Copy or cut rows to clipboard to paste later
   *
   * @fires beforeCopy
   * @fires copy
   * @param {Boolean} [isCut] Copies by default, pass `true` to cut
   * @category Common
   * @on-owner
   * @async
   */
  async copyRows(isCut = false, recordOrEvent) {
    var _a5, _b2;
    const me = this, { client, entityName } = me, { selectionMode } = client;
    if (me.cutOnly) {
      isCut = true;
    }
    let originalRecords = me.selectedRecords.filter((r) => !r.readOnly || !isCut);
    if (recordOrEvent) {
      let toCopyRecord = recordOrEvent;
      if (isKeyboardEvent(recordOrEvent)) {
        toCopyRecord = (_b2 = (_a5 = client.resolveRowRecord) == null ? void 0 : _a5.call(client, recordOrEvent.target)) != null ? _b2 : client.getRecordFromElement(recordOrEvent.target);
      }
      const copyAllRecords = selectionMode.multiSelect && selectionMode.cell && !client.features.cellCopyPaste || client.isSelected(toCopyRecord);
      if (!copyAllRecords) {
        originalRecords = [toCopyRecord];
      }
    }
    const clipboardData = await me.writeToClipboard(originalRecords, isCut);
    if (clipboardData === void 0) {
      return;
    }
    client.trigger("copy", { records: clipboardData.data, originalRecords, isCut, entityName });
    me.monitorCellCopyPaste();
  }
  // Called from Clipboardable when cutData changes
  setIsCut(record, isCut) {
    var _a5;
    (_a5 = this.grid.rowManager.getRowById(record)) == null ? void 0 : _a5.toggleCls("b-cut-row", isCut);
    record.meta.isCut = isCut;
  }
  // Called from Clipboardable when cutData changes
  handleCutData({ source }) {
    var _a5;
    if (source !== this && ((_a5 = this.cutData) == null ? void 0 : _a5.length)) {
      this.grid.store.remove(this.cutData);
    }
  }
  /**
   * Listens to CellCopyPaste beforePaste events
   * If user is trying to paste a string representation of a record from RowCopyPaste
   * It will return false and paste the record instead
   *
   * @internal
   */
  monitorCellCopyPaste() {
    const me = this, { cellCopyPaste } = me.client.features;
    if (cellCopyPaste && !me._cellCopyPasteListener) {
      me._cellCopyPasteListener = me.client.ion({
        beforePaste({ entityName, text, targetCell }) {
          if (entityName === cellCopyPaste.entityName && me.compareClipboardText(me.clipboardText, text)) {
            me.paste(targetCell.record);
            return false;
          }
        },
        thisObj: me
      });
    }
  }
  /**
   * Called from Clipboardable after writing a non-string value to the clipboard
   * @param eventRecords
   * @returns {String}
   * @private
   */
  stringConverter(records) {
    const { rowManager } = this.grid, { columnTypesToIgnore } = this, cells = records.flatMap((r) => {
      var _a5;
      return (_a5 = rowManager.getRowById(r)) == null ? void 0 : _a5.cells.map((c) => new GridLocation(c));
    });
    return this.cellsToString(cells.filter((c) => {
      var _a5;
      return c && !columnTypesToIgnore.includes((_a5 = c.column) == null ? void 0 : _a5.type);
    }));
  }
  cloneData({ data, isPaste }) {
    if ((data == null ? void 0 : data.length) && !this.cutOnly) {
      this.sortByIndex(data);
      data = this.extractParents([...data], {}, isPaste);
    }
    return data;
  }
  // Called from Clipboardable before writing to the clipboard
  async beforeCopy({ data, isCut }) {
    return await this.client.trigger("beforeCopy", { records: data, isCut, entityName: this.entityName });
  }
  /**
   * Paste rows below selected or passed record
   *
   * @fires beforePaste
   * @param {Core.data.Model} [record] Paste below this record, or currently selected record if left out
   * @category Common
   * @on-owner
   * @async
   * @returns {Core.data.Model[]} Pasted records
   */
  async pasteRows(record) {
    var _a5, _b2, _c2;
    const me = this, { client, entityName } = me, referenceRecord = record || client.selectedRecord, { tree } = client.store;
    if (client.readOnly || client.isTreeGrouped) {
      return [];
    }
    const clipboardData = await me.readFromClipboard({ referenceRecord }, true), isCut = clipboardData == null ? void 0 : clipboardData.isCut;
    let records = clipboardData == null ? void 0 : clipboardData.data;
    if (!clipboardData || !Array.isArray(records) || !(records == null ? void 0 : records.length) || tree && isCut && records.some((rec) => rec.contains(referenceRecord, true))) {
      return [];
    }
    if (isCut && tree) {
      records = me.extractParents(records, {});
    }
    await me.insertCopiedRecords(records, referenceRecord);
    if (client.isDestroying) {
      return;
    }
    if (isCut) {
      await me.clearClipboard();
    } else {
      client.selectedRecords = records;
    }
    client.trigger("paste", {
      records,
      originalRecords: clipboardData.originalData,
      referenceRecord,
      isCut,
      entityName
    });
    me.clipboard.triggerPaste(me);
    (_c2 = (_b2 = (_a5 = client.getRowFor(records[records.length - 1])) == null ? void 0 : _a5.cells) == null ? void 0 : _b2[0]) == null ? void 0 : _c2.focus();
    return records;
  }
  // Called from Clipboardable before finishing the internal clipboard read
  async beforePaste({ referenceRecord, data, originalData, text, isCut }) {
    const records = data !== text ? data : [];
    return await this.client.trigger("beforePaste", {
      records,
      referenceRecord,
      isCut,
      entityName: this.entityName,
      data,
      originalRecords: originalData
    });
  }
  /**
   * Called from Clipboardable after reading from clipboard, and it is determined that the clipboard data is
   * "external"
   * @param json
   * @private
   */
  stringParser(clipboardData) {
    return this.setFromStringData(clipboardData, true).modifiedRecords;
  }
  insertCopiedRecords(toInsert, recordReference) {
    const store = this.client.store.$master, insertAt = recordReference ? store.indexOf(recordReference) + 1 : store.count;
    if (store.isTree) {
      return ((recordReference == null ? void 0 : recordReference.parent) || store.rootNode).insertChild(toInsert, recordReference == null ? void 0 : recordReference.nextSibling, false, {
        // Specify node to insert before in the ordered tree. It allows to paste to a
        // correct place both ordered and visual.
        // Covered by TaskOrderedWbs.t.js
        orderedBeforeNode: recordReference == null ? void 0 : recordReference.nextOrderedSibling
      });
    } else {
      return store.insert(insertAt, toInsert);
    }
  }
  get selectedRecords() {
    const records = [...this.client.selectedRecords];
    this.client.selectedCells.forEach((cell) => {
      if (!records.includes(cell.record)) {
        records.push(cell.record);
      }
    });
    return records;
  }
  getMenuItemText(action, addRowSpecifier = false) {
    const me = this;
    let text = me[action + "RecordText"];
    if (addRowSpecifier) {
      text += ` (${me.selectedRecords.length > 1 ? me.rowSpecifierTextPlural : me.rowSpecifierText})`;
    }
    return text;
  }
  populateCellMenu({ record, items: items2, cellSelector }) {
    var _a5;
    const me = this, {
      client,
      rowOptionsOnCellContextMenu
    } = me, cellCopyPaste = ((_a5 = client.features.cellCopyPaste) == null ? void 0 : _a5.enabled) === true, targetIsCell = cellCopyPaste && client.isCellSelected(cellSelector);
    if (!client.isTreeGrouped && (record == null ? void 0 : record.isSpecialRow) === false && (cellCopyPaste ? client.selectedRows.length : client.selectedRecords.length) && (!targetIsCell || me.rowOptionsOnCellContextMenu)) {
      if (!me.copyOnly) {
        items2.cut = {
          text: me.getMenuItemText("cut", targetIsCell && rowOptionsOnCellContextMenu),
          localeClass: me,
          icon: "b-icon b-icon-cut",
          weight: 135,
          disabled: record.readOnly || client.isLockedRows || client.readOnly,
          onItem: () => me.cut(record)
        };
        items2.paste = {
          text: me.getMenuItemText("paste", targetIsCell && rowOptionsOnCellContextMenu),
          localeClass: me,
          icon: "b-icon b-icon-paste",
          weight: 140,
          onItem: () => me.paste(record),
          disabled: client.readOnly || me.hasClipboardData() === false || client.isLockedRows
        };
      }
      if (!me.cutOnly) {
        items2.copy = {
          text: me.getMenuItemText("copy", targetIsCell && rowOptionsOnCellContextMenu),
          localeClass: me,
          cls: "b-separator",
          icon: "b-icon b-icon-copy",
          weight: 120,
          onItem: () => me.copy(record)
        };
      }
    }
  }
  /**
   * Sort array of records ASC by its indexes stored in indexPath
   * @param {Core.data.Model[]} array array to sort
   * @private
   */
  sortByIndex(array) {
    const { store } = this.client;
    return array.sort((rec1, rec2) => {
      const idx1 = rec1.indexPath, idx2 = rec2.indexPath;
      if (!array.includes(rec1.parent) && !array.includes(rec2.parent)) {
        return store.indexOf(rec1) - store.indexOf(rec2);
      }
      if (idx1.length === idx2.length) {
        for (let i = 0; i < idx1.length; i++) {
          if (idx1[i] < idx2[i]) {
            return -1;
          }
          if (idx1[i] > idx2[i]) {
            return 1;
          }
        }
        return 0;
      } else {
        return idx1.length - idx2.length;
      }
    });
  }
  /**
   * Iterates over passed pre-sorted list of records and reassembles hierarchy of records.
   * @param {Core.data.Model[]} taskRecords array of records to extract parents from
   * @param {Object} idMap Empty object which will contain map linking original id with copied record
   * @param {Boolean} isPaste True if this function is called from a paste action
   * @returns {Core.data.Model[]} Returns array of new top-level nodes with children filled
   * @private
   */
  extractParents(taskRecords, idMap, isPaste = true) {
    const me = this, { store } = me.client;
    if (store.tree) {
      taskRecords.forEach((node) => {
        node.traverse((n) => {
          const parents = n.getTopParent(true);
          if (!taskRecords.includes(n) && (!me.isCut || !taskRecords.some((rec) => parents.includes(rec)))) {
            taskRecords.push(n);
          }
        });
      });
    }
    const result = taskRecords.reduce((parents, node) => {
      let copy;
      const parentId = node.parentId || node.meta.modified;
      if (me.isCut) {
        copy = node;
        copy.meta.isCut = false;
      } else {
        copy = node.copy();
        if (isPaste) {
          copy[me.nameField] = me.generateNewName(copy, node);
        }
        copy.data.expanded = node.isExpanded(me.client.store);
      }
      idMap[node.id] = copy;
      if (node.parent === store.rootNode) {
        parents.push(copy);
      } else if (parentId in idMap) {
        idMap[parentId].appendChild(copy, true);
      } else {
        parents.push(copy);
      }
      return parents;
    }, []);
    result.forEach((parent) => {
      parent.sortOrderedChildren(true, true);
    });
    return result;
  }
};
__publicField(RowCopyPaste, "$name", "RowCopyPaste");
__publicField(RowCopyPaste, "type", "rowCopyPaste");
__publicField(RowCopyPaste, "configurable", {
  /**
   * The field to use as the name field when updating the name of copied records
   * @config {String}
   * @default
   */
  nameField: "name",
  keyMap: {
    // Weight to give CellCopyPaste priority
    "Ctrl+C": { weight: 10, handler: "copy" },
    "Ctrl+X": { weight: 10, handler: "cut" },
    "Ctrl+V": { weight: 10, handler: "paste" }
  },
  copyRecordText: "L{copyRecord}",
  cutRecordText: "L{cutRecord}",
  pasteRecordText: "L{pasteRecord}",
  rowSpecifierText: "L{row}",
  rowSpecifierTextPlural: "L{rows}",
  localizableProperties: [
    "copyRecordText",
    "cutRecordText",
    "pasteRecordText",
    "rowSpecifierText",
    "rowSpecifierTextPlural"
  ],
  /**
   * Adds `Cut (row)`, `Copy (row)` and `Paste (row)` options when opening a context menu on a selected cell when
   * {@link Grid.view.mixin.GridSelection#config-selectionMode cellSelection} and
   * {@link Grid.feature.CellCopyPaste} is active. Default behaviour will only provide row copy/paste actions on a
   * selected row.
   * @config {Boolean}
   * @default
   */
  rowOptionsOnCellContextMenu: false,
  /**
   * If `true`, this translates copy actions to cut actions and removes the context menu `Copy` option.
   * @config {Boolean}
   * @default false
   */
  cutOnly: null,
  // Only allows copying full model data from other RowCopyPaste instances. Will use string values for others.
  allowedDataSources: ["RowCopyPaste"],
  /**
   * Specifies which column types should be omitted from copying.
   * @config {String[]}
   * @default ["rownumber","sequenceNumber"]
   * @internal
   */
  columnTypesToIgnore: ["rownumber", "sequenceNumber"]
});
RowCopyPaste.featureClass = "b-row-copypaste";
RowCopyPaste._$name = "RowCopyPaste";
GridFeatureManager.registerFeature(RowCopyPaste, true, "Grid");
GridFeatureManager.registerFeature(RowCopyPaste, false, "Gantt");
GridFeatureManager.registerFeature(RowCopyPaste, false, "SchedulerPro");
GridFeatureManager.registerFeature(RowCopyPaste, false, "ResourceHistogram");

// ../Grid/lib/Grid/feature/RowEdit.js
var RowEdit = class extends GridEditBase {
  bindStore(store) {
    this.detachListeners("store");
    store.ion({
      name: "store",
      update: "onStoreUpdate",
      remove: "onStoreRemove",
      thisObj: this
    });
  }
  changeTitleField(titleField) {
    if (typeof titleField === "string") {
      this.titleRenderer = (record) => this.L("L{Object.editing}", StringHelper.encodeHtml(record[titleField]));
    }
  }
  getEditingContext(cellSelector) {
    cellSelector = cellSelector.target ? new GridLocation(cellSelector.target) : this.grid.normalizeCellContext(cellSelector);
    if (!cellSelector.record.isSpecialRow) {
      return cellSelector;
    }
  }
  /**
   * Start editing specified row. If no cellContext is given it edits the first visible row.
   * This function is exposed on Grid and can thus be called as `grid.startEditing(...)`.
   *
   * @param {GridLocationConfig} cellContext Cell specified in format { id: 'x', columnId/column/field: 'xxx' }. See
   *   {@link Grid.view.Grid#function-getCell} for details.
   * @param {Boolean} [isAutoEdit] Pass `true` to indicate that the edit is due to a `keydown` event with {@link #config-autoEdit} set to
   *   `true`. In this case the editor field corresponding to the cell context will be cleared for the `keypress` to take effect upon focus.
   * @fires beforeStartRowEdit
   * @fires startRowEdit
   * @returns {Promise} Resolved promise returns`true` if editing has been started, `false` if an {@link Core.widget.Editor#event-beforeStart} listener
   *   has vetoed the edit.
   * @category Editing
   * @on-owner
   */
  async startEditing(editorContext = { id: this.grid.firstVisibleRow.id }, isAutoEdit) {
    var _a5, _b2, _c2;
    if (this.isEditing) {
      if (this.cancelEditing(true) === false) {
        return false;
      }
      (_b2 = (_a5 = this.editorContext) == null ? void 0 : _a5.row) == null ? void 0 : _b2.removeCls("b-editing");
    }
    editorContext = this.editorContext = this.getEditingContext(editorContext);
    if (editorContext) {
      const me = this, { grid } = me, editor = me.editor || (me.editor = me._editorConfig) && me.editor, { cancel } = editor.bbar.widgetMap;
      editorContext.editor = editor;
      editor.title = me.callback(me.titleRenderer, me, [editorContext.record]);
      editor.record = editorContext.record;
      editor.rowEditorContext = editorContext;
      editor.readOnly = grid.readOnly || editor.record.readOnly;
      cancel && (cancel.text = me.L(editor.readOnly ? "L{close}" : "L{cancel}"));
      if (grid.trigger("beforeStartRowEdit", { grid, editorContext }) === false) {
        return false;
      }
      if (me.revertButton) {
        editor.bbar.widgetMap.revert.hidden = !me.editorContext.record.isModified;
      }
      await editor.collapsible.toggleReveal(true);
      const field = editor.widgetMap[(_c2 = editorContext.column) == null ? void 0 : _c2.field], toFocus = field && (isAutoEdit || me.focusContextualField) ? field : editor;
      if (isAutoEdit && !editor.readOnly) {
        field == null ? void 0 : field.clear();
      }
      toFocus.focus();
      editorContext.row.addCls("b-editing");
      grid.trigger("startRowEdit", { grid, editorContext });
    }
  }
  onEditorStart() {
    if (this.triggerEvent !== "cellclick") {
      this.detachListeners("cellClickWhileEditing");
      this.grid.ion({
        name: "cellClickWhileEditing",
        cellclick: "onCellClickWhileEditing",
        thisObj: this
      });
    }
  }
  syncEditingReadOnly({ readOnly }) {
    if (this.isEditing) {
      const { editor } = this, { record } = this.editorContext, { cancel } = editor.bbar.widgetMap;
      editor.readOnly = readOnly || record.readOnly;
      cancel && (cancel.text = this.L(editor.readOnly ? "L{close}" : "L{cancel}"));
    }
  }
  onEditorHide() {
    var _a5, _b2;
    (_b2 = (_a5 = this.editorContext) == null ? void 0 : _a5.row) == null ? void 0 : _b2.removeCls("b-editing");
    this.detachListeners("cellClickWhileEditing");
    this.editorContext = this.editor.record = null;
  }
  /**
   * Finish editing, update the underlying record and hide the editor.
   * This function is exposed on Grid and can thus be called as `grid.finishEditing(...)`
   * @fires beforeFinishRowEdit
   * @fires finishRowEdit
   * @returns {Promise} Resolved promise returns `false` if the edit could not be finished due to an input field being invalid
   * or the {@link #event-beforeFinishRowEdit} event was prevented.
   * @on-owner
   */
  async finishEditing() {
    var _a5;
    const me = this, {
      grid,
      editor,
      editorContext
    } = this;
    if (editor.isValid) {
      let result = grid.trigger("beforeFinishRowEdit", editorContext);
      if (ObjectHelper.isPromise(result)) {
        me.finishEditingPromise = result;
        result = await result;
        me.finishEditingPromise = null;
      }
      if (result === false) {
        editor.focus();
        return false;
      }
      editor.collapsible.toggleReveal(false);
      me.updateRecordFromFields();
      grid.trigger("finishRowEdit", editorContext);
      return true;
    } else {
      (_a5 = editor.items.find((f) => !f.isValid)) == null ? void 0 : _a5.focus();
      return false;
    }
  }
  /**
   * Cancel editing, hides the editor.
   * This function is exposed on Grid and can thus be called as `grid.cancelEditing(...)`
   * @param {Boolean} isStarting Pass `true` to indicate that the cancel is due to a new edit starting
   * @fires beforeCancelRowEdit
   * @returns {Boolean} `false` if the edit could not be canceled due to the {@link #event-beforeCancelRowEdit} event being prevented.
   * @category Editing
   * @on-owner
   */
  cancelEditing(isStarting = false) {
    const me = this, { editor } = me;
    if (me.grid.trigger("beforeCancelRowEdit", me.editorContext) === false) {
      editor.focus();
      return false;
    }
    if (isStarting !== true) {
      if (me.instantUpdate) {
        me.isCanceling = true;
        editor.setValues(editor.initialValues);
        me.updateRecordFromFields();
        me.isCanceling = false;
      }
      editor.collapsible.toggleReveal(false);
    }
  }
  /**
   * Finish editing if clicking below rows (only applies when grid is higher than rows).
   * Also finish if event target is the subgrid which can happen if the pointer is moved during mouse down.
   * @private
   * @category Internal event handling
   */
  async onElementClick(event) {
    if (event.target.matches(".b-grid-body-container, .b-grid-subgrid") && this.editorContext) {
      await this.finishEditing(event);
    }
  }
  // Handles autoedit
  async onElementKeyDown(event) {
    const me = this, { grid } = me, { focusedCell } = grid;
    if (event.handled || !me.autoEdit || me.isEditing || !focusedCell || focusedCell.isActionable && DomHelper.isEditable(event.target) || event.ctrlKey) {
      return;
    }
    if (event.key.length <= 1) {
      me.startEditing(focusedCell, true);
    }
  }
  updateSide() {
    this.editor = null;
  }
  changeEditor(editor, was) {
    if (editor) {
      const me = this, {
        grid,
        labelWidth
      } = me, config = Panel.mergeConfigs({
        owner: grid,
        cls: {
          "b-editor-instant-update": me.instantUpdate
        },
        drawer: {
          side: me.side,
          size: me.editorSize
        },
        autoUpdateRecord: me.instantUpdate,
        items: me.collectEditorFields(),
        style: {
          [labelWidth ? "--b-label-width" : ""]: DomHelper.setLength(labelWidth)
        },
        refreshEdit: me.refreshEdit.bind(me)
      }, editor);
      if (!me._editorConfig) {
        me._editorConfig = editor;
      }
      if (me.local) {
        config.appendTo = grid.element;
      }
      if (me.revertButton) {
        config.bbar.items.revert = {
          text: "L{revert}",
          onClick: me.revertChanges.bind(me)
        };
      }
      editor = Panel.reconfigure(was, editor ? config : null, me);
      editor == null ? void 0 : editor.ion({
        thisObj: me,
        show: "onEditorStart",
        hide: "onEditorHide"
      });
    } else {
      was == null ? void 0 : was.removeAll();
      was == null ? void 0 : was.destroy();
    }
    return editor;
  }
  revertChanges() {
    const me = this, { editor, record } = me.editorContext;
    me.isCanceling = true;
    record.revertChanges();
    editor.title = me.callback(me.titleRenderer, me, [record]);
    editor.setValues(record, {
      onlyName: editor.strictRecordMapping,
      highlight: Boolean(editor.$highlight)
    });
    me.isCanceling;
  }
  refreshEdit() {
    const { editor } = this;
    if (editor.isVisible) {
      this.syncEditingReadOnly({ readOnly: this.grid.readOnly });
      if (!this.instantUpdate && !this.isCanceling) {
        editor.setValues(this.editorContext.record, {
          onlyName: editor.strictRecordMapping,
          highlight: Boolean(editor.$highlight)
        });
      }
    }
  }
  updateRecordFromFields() {
    const { editor } = this, { record } = editor;
    editor.items.forEach((field) => {
      const key = field.getValueName({
        onlyName: editor.strictRecordMapping
      });
      if (key && key in record) {
        record.setValue(key, field.value);
      }
    }, true);
  }
  updateInstantUpdate(instantUpdate) {
    const { _editor } = this;
    if (_editor) {
      _editor.autoUpdateRecord = instantUpdate;
      _editor.element.classList.toggle("b-editor-instant-update", Boolean(instantUpdate));
    }
  }
  collectEditorFields() {
    let weight = 0;
    return this.grid.columns.reduce((items2, column) => {
      const { editor } = column, { modelClass } = this.grid.store;
      if (editor == null ? void 0 : editor.isField) {
        editor.readOnly = column.readOnly;
        weight = editor.weight || (editor.weight = weight + 100);
        if (!editor.label) {
          editor.label = column.text || modelClass.getFieldDefinition(column.field).label;
        }
        editor.revertOnEscape = column.revertOnEscape;
        items2[column.field] = editor;
      }
      return items2;
    }, {});
  }
};
__publicField(RowEdit, "$name", "RowEdit");
__publicField(RowEdit, "configurable", {
  /**
   * The {@link Core.widget.Panel} instance used as the editor.
   * @prp {Core.widget.Panel} editor The editor panel used to edit rows.
   * @accepts {PanelConfig}
   */
  editor: {
    $config: "lazy",
    value: {
      type: "panel",
      cls: {
        "b-row-editor": 1
      },
      drawer: {
        type: "overlay",
        autoClose: false,
        tool: null
      },
      keyMap: {
        Escape: "up.cancelEditing",
        Enter(e) {
          if (e.target.matches("button")) {
            return false;
          } else {
            this.callback("up.finishEditing");
          }
        }
      },
      bbar: {
        items: {
          save: {
            text: "L{save}",
            cls: { "b-raised": 1 },
            color: "b-blue",
            onClick: "up.finishEditing"
          },
          cancel: {
            text: "L{cancel}",
            onClick: "up.cancelEditing"
          }
        }
      },
      focus() {
        const target = this.isValid ? this.focusableElement : this.items.find((f) => !f.isValid).focusableElement;
        target.focus({ preventScroll: true });
      }
    }
  },
  /**
   * By default, the editor field corresponding to the cell clicked will be focused when starting editing.
   *
   * Set to `false` to prevent this. Focus will then go to the first field in the editor.
   *
   * if {@link #config-autoEdit} is `true`, starting editing by typing will always focus the contextual field in
   * the editor.
   * @config {Boolean}
   * @default
   */
  focusContextualField: true,
  /**
   * Set to `true` to update the record as soon as a field is changed.
   * @config {Boolean} [instantUpdate=false]
   */
  instantUpdate: null,
  /**
   * The width of the editor. (Or height if {@link #config-side} is `'top'` or `'bottom'`).
   *
   * May be specified as a number of pixels or a CSS length string.
   * @config {Number|String}
   * @default '30em'
   */
  editorSize: null,
  /**
   * The width of field labels in the editor.
   *
   * May be specified as a number of pixels or a CSS length string.
   * @config {Number|String}
   * @default '10em'
   */
  labelWidth: null,
  /**
   * Configure this as `true` to dock the editor to the grid's element instead of the browser viewport.
   * @config {Boolean} local
   * @default false
   */
  local: null,
  /**
   * Configure this as `true` to show a "Revert" button in the editor which reverts any unsynced changes to the record.
   *
   * This reverts all changes made since the data was loaded or synchronized with the server. This is different to just
   * canceling an ongoing edit. This reverts the record to its loaded state.
   *
   * The button is only shown when starting an edit on a record which has unsynced changes.
   * @config {Boolean}
   * @default false
   */
  revertButton: null,
  /**
   * The side of the grid where the editor will be shown.
   *
   * `'start'` means the `inline-start` side.
   * `'end'` means the `inline-end` side.
   * @config {'start'|'left'|'end'|'right'|'top'|'bottom'}
   * @default 'end'
   */
  side: {
    $config: "lazy",
    value: "end"
  },
  keyMap: {
    Enter: "startEditing",
    F2: "startEditing"
  },
  /**
   * The name of the field from the record to use when generating the title for the editor.
   *
   * The default implementation returns "Editing " plus the value of the named field from the editing record.
   * @config {String} [titleField='name']
   */
  titleField: "name",
  /**
   * A function (or the name of a function) which will be called passing the editing record to generate the title
   * for the editor.
   *
   * <div class="note">
   * You should never modify any records inside this method.
   * </div>
   *
   * The default implementation returns "Editing " (localized according to the current locale) plus the value of
   * the field specified in {@link #config-titleField}.
   *
   * This may be configured if a more sophisticated title is required.
   * @config {Function} [titleRenderer]
   * @param {Core.data.Model} record The record being edited
   * @returns {String} The title for the editor
   */
  titleRenderer: null
});
RowEdit._$name = "RowEdit";
GridFeatureManager.registerFeature(RowEdit);

// ../Grid/lib/Grid/feature/RowExpander.js
var actions3 = {
  onTab: 1,
  onShiftTab: 1
};
var storeRemoveActions = { remove: 1, filter: 1, dataset: 1, replace: 1 };
var RowExpander = class extends InstancePlugin.mixin(Delayable_default) {
  // Cannot use `static properties = {}`, new Map/Set would pollute the prototype
  static get properties() {
    return {
      // CSS classes
      expanderBodyClass: "b-rowexpander-body",
      expandedRowClass: "b-rowexpander-row-expanded",
      shadowRootContainerClass: "b-rowexpander-shadowroot-container",
      // Map where the keys are the expanded records and values are an object
      // {rowHeight, cellHeight, expandedBodyElements}
      recordStateMap: /* @__PURE__ */ new Map(),
      collapsingStateMap: /* @__PURE__ */ new Map()
    };
  }
  // Plugin configuration. This plugin chains/overrides some of the functions in Grid.
  static get pluginConfig() {
    return {
      chain: ["afterColumnsChange", "beforeRenderRow", "processRowHeight", "bindStore", "navigateUp"],
      override: ["onGridBodyFocusIn", "navigateDown", "catchFocus", "keyMapOnKeyDown"],
      assign: ["expandRow", "collapseRow"]
    };
  }
  //endregion
  //region Init
  afterConstruct() {
    const me = this, { client } = me;
    if (!me.renderer && !me.widget) {
      me.disabled = true;
      console.warn("RowExpander requires either a widget config or implementing the renderer function.");
      return;
    }
    if (client.isGanttBase && client.fixedRowHeight !== false) {
      console.warn("When using RowExpander on a Gantt, the Gantt`s fixedRowHeight config must be set to `false`.");
    }
    if (me.widget) {
      GlobalEvents_default.ion({
        theme: me.onThemeChange,
        thisObj: me
      });
    }
    me.bindStore(client.store);
    if (me.triggerEvent) {
      client.ion({ [me.triggerEvent]: "onTriggerEvent", thisObj: me });
    }
    me.addColumn();
  }
  bindStore(store) {
    const me = this;
    me.recordStateMap.clear();
    me.collapsingStateMap.clear();
    me.detachListeners("clientStoreChange");
    store.ion({
      name: "clientStoreChange",
      change: me.onStoreChange,
      thisObj: me
    });
  }
  doDisable(disable) {
    const { client } = this;
    if (disable) {
      this.recordStateMap.clear();
      this.collapsingStateMap.clear();
    }
    if (!client.isConfiguring) {
      client.rowManager.renderFromRow();
    }
    super.doDisable(disable);
  }
  changeLoadingIndicatorText(text) {
    return text ? this.L(text) : text;
  }
  // Overrides onGridBodyFocusIn to ignore events on row expander body.
  onGridBodyFocusIn(event) {
    var _a5, _b2;
    const me = this;
    if (me.hasWidget ? !event.target.matches(`.${me.expanderBodyClass}, .${me.shadowRootContainerClass}`) : !((_b2 = (_a5 = me.client.lastMousedownEvent) == null ? void 0 : _a5.target) == null ? void 0 : _b2.closest("." + me.expanderBodyClass))) {
      me.overridden.onGridBodyFocusIn(event);
    }
  }
  // Override keyMap key down so to not acting on keydown inside nested grid
  keyMapOnKeyDown({ target }) {
    var _a5;
    if (!this.hasWidget || !((_a5 = target.classList) == null ? void 0 : _a5.contains(this.shadowRootContainerClass))) {
      this.overridden.keyMapOnKeyDown(...arguments);
    }
  }
  get isAnimating() {
    return this.client.isAnimating;
  }
  set isAnimating(value2) {
    const { client } = this, wasAnimating = client.isAnimating;
    client.isAnimating = value2;
    if (client.isAnimating !== wasAnimating) {
      client.element.classList.toggle("b-rowexpander-animating");
    }
  }
  changeWidget(widget) {
    if (widget && this.isWidgetConfig(widget)) {
      return { [this.client.regions[0]]: widget };
    }
    return widget;
  }
  //endregion
  //region Deprecated events
  /**
   * This event fires before row expand is started.
   *
   * Returning `false` from a listener prevents the RowExpander to expand the row.
   *
   * Note that this event fires when the RowExpander toggles the row, not when the actual row expander body is
   * rendered. Most of the time this is synchronous, but in the case of a row that is not yet rendered into view by
   * scrolling, it can happen much later.
   *
   * @event beforeExpand
   * @deprecated 6.0.0 Use the `beforeRowExpand` event on the Grid itself
   * @preventable
   * @async
   * @param {Core.data.Model} record Record
   */
  /**
   * This event fires before row collapse is started.
   *
   * Returning `false` from a listener prevents the RowExpander to collapse the row.
   *
   * Note that this event fires when the RowExpander toggles the row, not when the actual row expander body is
   * rendered. Most of the time this is synchronous, but in the case of a row that is not yet rendered into view by
   * scrolling, it can happen much later.
   *
   * @event beforeCollapse
   * @deprecated 6.0.0 Use the `beforeRowCollapse` event on the Grid itself
   * @preventable
   * @async
   * @param {Core.data.Model} record Record
   */
  /**
   * This event fires when a row expand has finished expanding.
   *
   * Note that this event fires when actual row expander body is rendered, and not necessarily in immediate succession
   * of an expand action. In the case of expanding a row that is not yet rendered into view by scrolling, it can happen
   * much later.
   *
   * @event expand
   * @param {Core.data.Model} record Record
   * @param {Object} expandedElements An object with the Grid region name as property and the expanded body
   * element as value
   * @param {Core.widget.Widget} widget In case of expanding a Widget, this will be a reference to the instance
   * created by the actual expansion. If there is multiple Grid regions, use the `widgets` param instead.
   * @param {Object} widgets In case of expanding a Widget, this will be an object with the Grid region name as
   * property and the reference to the widget instance created by the actual expansion
   * @deprecated 6.0.0 Use the `rowExpand` event on the Grid itself
   */
  /**
   * This event fires when a row has finished collapsing.
   *
   * @event collapse
   * @param {Core.data.Model} record Record
   * @deprecated 6.0.0 Use the `rowCollapse` event on the Grid itself
   */
  //endregion
  //region Events
  /**
   * This event fires before row expand is started.
   *
   * Returning `false` from a listener prevents the RowExpander to expand the row.
   *
   * Note that this event fires when the RowExpander toggles the row, not when the actual row expander body is
   * rendered. Most of the time this is synchronous, but in the case of a row that is not yet rendered into view by
   * scrolling, it can happen much later.
   *
   * @event beforeRowExpand
   * @preventable
   * @async
   * @param {Core.data.Model} record Record
   * @on-owner
   */
  /**
   * This event fires before row collapse is started.
   *
   * Returning `false` from a listener prevents the RowExpander to collapse the row.
   *
   * Note that this event fires when the RowExpander toggles the row, not when the actual row expander body is
   * rendered. Most of the time this is synchronous, but in the case of a row that is not yet rendered into view by
   * scrolling, it can happen much later.
   *
   * @event beforeRowCollapse
   * @preventable
   * @async
   * @param {Core.data.Model} record Record
   * @on-owner
   */
  /**
   * This event fires when a row expand has finished expanding.
   *
   * Note that this event fires when actual row expander body is rendered, and not necessarily in immediate succession
   * of an expand action. In the case of expanding a row that is not yet rendered into view by scrolling, it can happen
   * much later.
   *
   * @event rowExpand
   * @param {Core.data.Model} record Record
   * @param {Object} expandedElements An object with the Grid region name as property and the expanded body
   * element as value
   * @param {Core.widget.Widget} widget In case of expanding a Widget, this will be a reference to the instance
   * created by the actual expansion. If there is multiple Grid regions, use the `widgets` param instead.
   * @param {Object} widgets In case of expanding a Widget, this will be an object with the Grid region name as
   * property and the reference to the widget instance created by the actual expansion
   * @on-owner
   */
  /**
   * This event fires when a row has finished collapsing.
   *
   * @event rowCollapse
   * @param {Core.data.Model} record Record
   * @on-owner
   */
  //endregion
  //region ExpanderColumn
  afterColumnsChange() {
    this.addColumn();
  }
  changeColumn(config) {
    const me = this, { client } = me;
    if (config == null) {
      return config;
    }
    let collapseIconDirection = me.columnPosition === "first" ? "left" : "right";
    if (client.isRTL) {
      collapseIconDirection = me.columnPosition === "first" ? "right" : "left";
    }
    return ObjectHelper.assign({
      type: "action",
      draggable: false,
      resizable: false,
      showColumnPicker: false,
      ariaLabel: me.L("L{RowExpander.columnDescription}"),
      actions: [{
        cls: `b-icon b-icon-collapse-${collapseIconDirection}`,
        tooltip: ({ record }) => me.L(me.recordStateMap.has(record) ? "L{RowExpander.collapse}" : "L{RowExpander.expand}"),
        onClick: ({ record }) => me.toggleExpand(record)
      }],
      width: 40,
      hideable: false,
      align: "center",
      region: client.regions[0],
      field: "expanderActionColumn"
    }, config);
  }
  // Called in construct and if grid columns change
  addColumn() {
    const me = this, { column } = me, { columns } = me.client;
    if (!me._isAddingExpanderColumn && column && (!me._expander || !columns.includes(me._expander))) {
      me._isAddingExpanderColumn = true;
      if (me.columnPosition === "last") {
        [me._expander] = columns.add(column);
        me.client.fillLastColumn = false;
      } else {
        [me._expander] = columns.insert(0, column);
      }
      me._isAddingExpanderColumn = false;
    }
  }
  //endregion
  //region UI events
  onTriggerEvent({ target }) {
    if (this.disabled || (target == null ? void 0 : target.closest(".b-action-cell")) || !target.closest(".b-grid-cell")) {
      return;
    }
    this.toggleExpand(this.client.getRecordFromElement(target));
  }
  /**
   * Toggles expanded state.
   * @private
   * @param {Core.data.Model} record The record that should be toggled
   * @category Internal
   */
  toggleExpand(record) {
    if (record) {
      if (this.recordStateMap.has(record)) {
        this.collapse(record);
      } else {
        this.expand(record);
      }
    }
  }
  onExpanderBodyResize(entries) {
    var _a5;
    for (const entry of entries) {
      const record = this.client.store.getById((_a5 = entry.target.dataset) == null ? void 0 : _a5.id), recordState = record && this.recordStateMap.get(record);
      if (recordState && !recordState.ignoreResize && !recordState.isAnimating) {
        const oldHeight = recordState.expanderBodyHeight;
        recordState.expanderBodyHeight = null;
        if (this.processRowHeight(record, 0) !== oldHeight) {
          this.renderRowsWithAnimation(record);
        }
      }
    }
  }
  //endregion
  //region Rendering
  get shouldSpanRegions() {
    return this.spanRegions && this.client.regions.length > 1;
  }
  /**
   * Listens to changes in the Grid Store. Will remove expand State data on Store removal.
   * If the refreshOnRecordChange config is `true`, it will trigger a re-render of the expander.
   * @private
   * @param {String} action
   * @param {Core.data.Store} source
   * @param {Core.data.Model[]} records
   * @category Internal
   */
  onStoreChange({ action, source, records, changes }) {
    var _a5, _b2;
    const me = this, { recordStateMap, collapsingStateMap } = me, changedKeys = changes && Object.keys(changes);
    if ((changedKeys == null ? void 0 : changedKeys.length) === 1 && ((_a5 = source.modelClass.fieldMap[changedKeys[0]]) == null ? void 0 : _a5.type) === "store") {
      return;
    }
    if (me.disabled) {
      return;
    }
    if (action === "removeAll") {
      recordStateMap.clear();
      collapsingStateMap.clear();
    } else if (storeRemoveActions[action]) {
      for (const [record, state] of recordStateMap) {
        if (!source.includes(record)) {
          me.destroyWidgets(state);
          recordStateMap.delete(record);
          collapsingStateMap.delete(record);
        }
      }
    } else if (me.refreshOnRecordChange && (records == null ? void 0 : records.length)) {
      if (action === "update") {
        const pointerDown = (_b2 = GlobalEvents_default.currentPointerDown) == null ? void 0 : _b2.target, recordsToRender = [];
        let delayUntilMouseUp = false;
        records.forEach((record) => {
          const recordState = recordStateMap.get(record);
          if (recordState == null ? void 0 : recordState.isCreated) {
            recordState.isCreated = false;
            if (!delayUntilMouseUp && pointerDown && me.client.regions.some((region) => {
              var _a6, _b3, _c2, _d, _e;
              return ((_b3 = (_a6 = recordState.expandedBodyElements) == null ? void 0 : _a6[region]) == null ? void 0 : _b3.contains(pointerDown)) || ((_e = (_d = (_c2 = recordState.widgets) == null ? void 0 : _c2[region]) == null ? void 0 : _d.element) == null ? void 0 : _e.contains(pointerDown));
            })) {
              delayUntilMouseUp = true;
            }
            recordsToRender.push(record);
          }
        });
        if (recordsToRender == null ? void 0 : recordsToRender.length) {
          if (delayUntilMouseUp) {
            me.client.delayUntilMouseUp(() => {
              recordsToRender.forEach((r) => me.bufferedRenderer(r));
            });
          } else {
            me.client.rowManager.renderFromRecord(recordsToRender[0]);
          }
        }
      }
    }
  }
  // Implements grid.processRowHeight hook
  processRowHeight(record, height) {
    var _a5, _b2;
    const me = this, { shouldSpanRegions } = me;
    let recordState = me.recordStateMap.get(record), heightChanged = false;
    if (recordState && !recordState.expanderBodyHeight) {
      for (const region of me.client.regions) {
        const height2 = (_a5 = recordState.expandedBodyElements[region]) == null ? void 0 : _a5.offsetHeight;
        if (height2 > recordState.expanderBodyHeight) {
          recordState.expanderBodyHeight = height2;
          heightChanged = true;
        }
      }
    } else if (!recordState && (recordState = me.collapsingStateMap.get(record)) && recordState.expanderBodyHeight) {
      recordState.expanderBodyHeight = 0;
      heightChanged = true;
    }
    if (heightChanged && me.enableAnimations && shouldSpanRegions) {
      me.animateSpannedExpander(recordState);
    }
    return ((_b2 = recordState == null ? void 0 : recordState.expanderBodyHeight) != null ? _b2 : 0) + height;
  }
  // If we're using spanRegions, we need to force a CSS transition on the body elements height
  animateSpannedExpander(state) {
    const me = this, bodyElement = state.expandedBodyElements[me.client.regions[0]], rowElement = me.client.rowManager.getRowById(state.record).element;
    state.isAnimating = me.isAnimating = true;
    state.ignoreResize = true;
    bodyElement.style.height = rowElement.offsetHeight - state.cellHeight + "px";
    bodyElement.offsetHeight;
    bodyElement.style.height = state.expanderBodyHeight + "px";
    me.waitForTransition(bodyElement, () => {
      bodyElement.style.height = "";
      if (!state.expanderBodyHeight) {
        bodyElement.remove();
      }
      state.isAnimating = me.isAnimating = false;
    });
  }
  /**
   * Hooks on before row render to render or remove row expander content depending on record state.
   * @private
   * @category Internal
   */
  beforeRenderRow({ row, record }) {
    const me = this, { regions } = me.client, {
      expandedRowClass,
      collapsingStateMap,
      shouldSpanRegions
    } = me, recordState = me.recordStateMap.get(record);
    row.cls.toggle("b-rowexpander-disabled", me.disabled);
    if (row.cls[expandedRowClass]) {
      let collapsingState = collapsingStateMap.get(record);
      if (collapsingState) {
        const clearCollapse = () => {
          collapsingState = collapsingStateMap.get(record);
          if (collapsingState) {
            collapsingStateMap.delete(record);
            me.removeExpander(row);
            me.destroyWidgets(collapsingState);
          }
        };
        if (me.enableAnimations && me.isAnimating) {
          me.waitForTransition(row, clearCollapse);
        } else {
          clearCollapse();
        }
      } else if (!recordState) {
        me.removeExpander(row);
      }
    } else {
      collapsingStateMap.delete(record);
    }
    if (!me.disabled && recordState) {
      if (!recordState.isCreated) {
        recordState.ignoreResize = true;
        me.renderExpander(record, row, recordState);
      }
      row.cls.add(expandedRowClass);
      regions.forEach((region, i) => {
        const rowElement = row.getElement(region);
        if (recordState.isCreated && (!shouldSpanRegions || i === 0)) {
          const bodyElement = recordState.expandedBodyElements[region], target = shouldSpanRegions ? me.client.verticalScroller : rowElement;
          if (bodyElement.parentElement !== target) {
            if (!shouldSpanRegions) {
              DomHelper.removeEachSelector(rowElement, "." + me.expanderBodyClass);
            }
            target.appendChild(bodyElement);
            me.observeResize(bodyElement);
          }
          if (shouldSpanRegions) {
            bodyElement.style.top = row.top + recordState.cellHeight + "px";
            row._spannedExpandedBodyElement = bodyElement;
          }
          recordState.ignoreResize = false;
          if (recordState.renderPromiseResolver && !recordState.isRenderingAsync) {
            me.delay(recordState.renderPromiseResolver);
            recordState.renderPromiseResolver = null;
          }
        }
        me.lockCellHeight(rowElement, recordState.cellHeight, false);
      });
      if (me._shouldScrollIntoView && me.autoScroll) {
        me._shouldScrollIntoView = false;
        if (!DomHelper.isInView(recordState.expandedBodyElements[regions[0]], true)) {
          me.client.rowManager.ion({
            once: true,
            thisObj: me,
            renderDone: () => me.scrollRowIntoView(row, record)
          });
        }
      }
    }
  }
  /**
   * Scrolls expanded row into view. This function is called after rowManager has finished rendering.
   * @private
   * @category Internal
   */
  scrollRowIntoView(row, record) {
    if (this.isAnimating) {
      this.waitForTransition(row, () => this.client.scrollRowIntoView(record));
    } else {
      this.client.scrollRowIntoView(record);
    }
  }
  /**
   * Waits for height transition on the provided rows element. Then calls provided function.
   * @private
   * @category Internal
   */
  waitForTransition(row, fn2) {
    var _a5;
    const element = DomHelper.isElement(row) ? row : row.element;
    EventHelper.onTransitionEnd({
      element,
      property: "height",
      handler: fn2,
      thisObj: this,
      duration: (_a5 = DomHelper.getPropertyTransitionDuration(element, "height")) != null ? _a5 : 1
    });
  }
  removeExpander(row) {
    var _a5, _b2;
    const me = this;
    row.cls.remove(me.expandedRowClass);
    for (const region of me.client.regions) {
      const rowElement = row.getElement(region);
      for (const child of rowElement.querySelectorAll("." + me.expanderBodyClass)) {
        me.unobserveResize(child);
        child.remove();
      }
      rowElement.classList.remove(me.expandedRowClass);
      me.lockCellHeight(rowElement, null, false);
      if (me.shouldSpanRegions) {
        (_b2 = (_a5 = row._spannedExpandedBodyElement) == null ? void 0 : _a5.remove) == null ? void 0 : _b2.call(_a5);
        row._spannedExpandedBodyElement = null;
      }
    }
  }
  destroyWidgets(state) {
    var _a5, _b2, _c2;
    for (const region of this.client.regions) {
      (_c2 = (_b2 = (_a5 = state.widgets) == null ? void 0 : _a5[region]) == null ? void 0 : _b2.destroy) == null ? void 0 : _c2.call(_b2);
    }
  }
  // Checks if a configuration object is a widget configuration object
  isWidgetConfig(content) {
    return content.type && !content.tag;
  }
  /**
   * Creates expander element for each grid region and calls the renderer, also for each grid region.
   * @private
   * @param {Core.data.Model} record
   * @param {Grid.row.Row} row
   * @param {Object} recordState
   * @category Internal
   */
  renderExpander(record, row, recordState) {
    var _a5, _b2;
    const me = this, { client: grid, widget, shouldSpanRegions } = me, cellHeight = (_a5 = row.cells[0]) == null ? void 0 : _a5.offsetHeight, { expandedBodyElements = {} } = recordState, renderPromises = [], continueRendering = (content, expanderElement, region) => {
      if (content != null) {
        if (typeof content === "string") {
          const currentChildren = [...expanderElement.childNodes].map((n) => expanderElement.removeChild(n));
          expanderElement.innerHTML = content;
          currentChildren.reverse().forEach((n) => expanderElement.insertBefore(n, expanderElement.firstChild));
        } else if (me.isWidgetConfig(content)) {
          createWidget(content, expanderElement, region);
        } else {
          content = DomHelper.createElement(content);
          expanderElement.appendChild(content);
        }
      }
    }, createWidget = (widgetConfig, expanderElement, region) => {
      if (!expanderElement.attachShadow) {
        console.warn("Rendering a Widget in the RowExpander requires access to the Shadow DOM. If your app lives in a sandboxed environment with restrictions on Shadow DOM manipulation, this is not supported. If you use SalesForce LWC, consider enabling Lightning Web Security.");
        return;
      }
      const shadowRootContainer = DomHelper.createElement({
        parent: expanderElement,
        className: me.shadowRootContainerClass,
        style: "flex : 1"
      }), shadowRoot = shadowRootContainer._shadowRoot = shadowRootContainer.attachShadow({ mode: "closed" });
      renderPromises.push(DomHelper.cloneStylesIntoShadowRoot(shadowRoot).then(() => {
        var _a6, _b3;
        const themeName = (_a6 = DomHelper.getThemeInfo()) == null ? void 0 : _a6.name;
        if (grid.isDestroyed) {
          return;
        }
        const widgetDataField = (_b3 = widgetConfig.dataField) != null ? _b3 : me.dataField;
        if (widgetDataField) {
          const fieldData = record.getValue(widgetDataField);
          if (fieldData == null ? void 0 : fieldData.isStore) {
            widgetConfig.store = fieldData;
          } else if (grid.store[`${widgetDataField}Store`]) {
            const relatedStore = grid.store[`${widgetDataField}Store`];
            widgetConfig.store = relatedStore.chain((r) => record.getValue(widgetDataField).includes(r));
          } else {
            widgetConfig.store = {
              data: fieldData,
              // Refresh expanded row on created store changes
              internalListeners: {
                change: () => !row.isDestroyed && row.render(),
                thisObj: me
              }
            };
          }
        }
        if (themeName) {
          const { cls } = widgetConfig, themeCls = `b-theme-${themeName.toLowerCase()}`;
          widgetConfig.cls = cls ? cls + " " + themeCls : themeCls;
        }
        if (!recordState.widgets) {
          recordState.widgets = {};
        }
        recordState.widgets[region] = expanderElement.widget = Widget.create(ObjectHelper.assign({
          appendTo: shadowRoot,
          owner: grid,
          flex: 1,
          minHeight: "5em",
          isNested: true,
          expandedRecord: record
        }, widgetConfig));
      }).catch((href) => {
        throw new Error("Could not load stylesheet " + href);
      }));
    };
    if (recordState.isRenderingAsync) {
      return;
    }
    row.addCls(me.expandedRowClass);
    Object.assign(recordState, {
      cellHeight,
      expandedBodyElements,
      expanderBodyHeight: 0,
      loadingIndicators: []
    });
    for (const [i, region] of grid.regions.entries()) {
      if (shouldSpanRegions && i > 0) {
        break;
      }
      const rowElement = row.getElement(region);
      let expanderBodyElement = expandedBodyElements[region], renderResponse;
      if (expanderBodyElement) {
        me.unobserveResize(expanderBodyElement);
        expanderBodyElement.innerHTML = "";
      } else {
        expanderBodyElement = DomHelper.createElement({
          parent: shouldSpanRegions ? grid.verticalScroller : rowElement,
          tabIndex: -1,
          className: me.expanderBodyClass,
          style: {
            top: shouldSpanRegions ? row.top + cellHeight : cellHeight + "px"
          },
          dataset: {
            id: record.id
          }
        });
      }
      me.observeResize(expanderBodyElement);
      if (widget == null ? void 0 : widget[region]) {
        createWidget(widget[region], expanderBodyElement, region);
      } else {
        renderResponse = (_b2 = me.renderer) == null ? void 0 : _b2.call(me, {
          record,
          expanderElement: expanderBodyElement,
          rowElement,
          region,
          grid
        });
      }
      if (Objects.isPromise(renderResponse)) {
        renderPromises.push(renderResponse.then((content) => continueRendering(content, expanderBodyElement, region)));
      } else {
        continueRendering(renderResponse, expanderBodyElement, region);
      }
      expandedBodyElements[region] = expanderBodyElement;
    }
    if (renderPromises.length) {
      recordState.isRenderingAsync = true;
      me.showLoadingIndicators(recordState);
      Promise.all(renderPromises).then(() => {
        Promise.all(renderPromises).then(() => {
          var _a6, _b3;
          if (grid.isDestroyed) {
            return;
          }
          (_a6 = recordState.loadingIndicators) == null ? void 0 : _a6.forEach((li) => li.remove());
          recordState.loadingIndicators.length = 0;
          recordState.ignoreResize = false;
          recordState.isRenderingAsync = false;
          for (const [, state] of me.recordStateMap) {
            if (state.isRenderingAsync) {
              return;
            }
          }
          (_b3 = me.renderRowsWithAnimation) == null ? void 0 : _b3.call(me, record, true);
        });
      });
    }
    recordState.isCreated = true;
  }
  showLoadingIndicators(recordState) {
    for (const region in recordState.expandedBodyElements) {
      recordState.loadingIndicators.push(DomHelper.createElement({
        parent: recordState.expandedBodyElements[region],
        className: "b-rowexpander-loading",
        style: {
          height: this.loadingIndicatorHeight + "px"
        },
        children: [
          {
            tag: "i",
            className: "b-icon b-icon-spinner"
          },
          this.loadingIndicatorText
        ]
      }));
    }
  }
  /**
   * Called when grid rows needs to re-render, for example on expand or collapse.
   * Activates animations on grid, and deactivates them when they are completed.
   * @private
   * @param {Core.data.Model} record Record whose row was toggled
   * @category Internal
   */
  renderRowsWithAnimation(record) {
    const me = this;
    if (me.enableAnimations) {
      const row = me.client.rowManager.getRowById(record);
      if (row) {
        me.isAnimating = true;
        if (me.collapsingStateMap.has(record)) {
          row.addCls("b-row-is-collapsing");
        }
        me.waitForTransition(row, () => {
          var _a5;
          me.isAnimating = false;
          if (!row.isDestroyed) {
            (_a5 = row.removeCls) == null ? void 0 : _a5.call(row, "b-row-is-collapsing");
          }
        });
      }
    }
    return me.bufferedRenderer(record);
  }
  /**
   * Collects a rendering call for each record, saves them in array and calls the delayed (RAF) rafRenderer function
   * @private
   * @param {Core.data.Model} record Record whose row was toggled
   * @category Internal
   */
  bufferedRenderer(record) {
    var _a5;
    ((_a5 = this._bufferedRecords) != null ? _a5 : this._bufferedRecords = []).push(record);
    if (!this._rafPromise) {
      this._rafPromise = new Promise((resolve) => {
        requestAnimationFrame(() => {
          var _a6;
          (_a6 = this.internalRender) == null ? void 0 : _a6.call(this, resolve);
          this._rafPromise = null;
        });
      });
    }
    return this._rafPromise;
  }
  /**
   * Re-renders the grid from the topmost record of those saved in bufferedRenderer
   * @private
   * @category Internal
   */
  internalRender(resolvePromise) {
    const me = this, { _bufferedRecords } = me, { store } = me.client;
    me.recordStateMap.forEach((state, record) => {
      if (state.renderPromiseResolver && state.isCreated && !_bufferedRecords.includes(record)) {
        _bufferedRecords.push(record);
      }
    });
    const [top] = _bufferedRecords.sort((a, b) => store.indexOf(a) - store.indexOf(b));
    me.client.rowManager.renderFromRecord(top);
    _bufferedRecords.length = 0;
    me.delay(resolvePromise);
  }
  /**
   * Called when row is expanded. This function locks all cell's height to current height (before expanding).
   * @private
   * @param {HTMLElement} rowElement
   * @param {Number} cellHeight The height to lock
   * @param {Boolean} unlock To remove locked cell height when the row is collapsed
   * @category Internal
   */
  lockCellHeight(rowElement, cellHeight, unlock) {
    for (let a = 0; a < rowElement.children.length; a++) {
      const child = rowElement.children[a];
      if (!child.classList.contains(this.expanderBodyClass)) {
        child.style.height = unlock ? "" : cellHeight + "px";
      }
    }
  }
  //endregion
  //region Public
  /**
   * Tells the RowExpander that the provided record should be expanded. If or when the record is rendered into view,
   * the record will be expanded.
   *
   * Promise will resolve when the row gets expanded. Note that this can be much later than the actual expand call,
   * depending on response times and if current record is in view or not.
   *
   * @param {Core.data.Model} record Record whose row should be expanded
   * @category Rows
   * @on-owner
   */
  async expandRow(...args) {
    return this.expand(...args);
  }
  /**
   * Tells the RowExpander that the provided record should be collapsed. If the record is in view, it will be
   * collapsed. If the record is not in view, it will simply not be expanded when rendered into view.
   *
   * @param {Core.data.Model} record Record whose row should be collapsed
   * @category Rows
   * @on-owner
   */
  async collapseRow(...args) {
    return this.collapse(...args);
  }
  /**
   * Tells the RowExpander that the provided record should be expanded. If or when the record is rendered into view,
   * the record will be expanded.
   *
   * Promise will resolve when the row gets expanded. Note that this can be much later than the actual expand call,
   * depending on response times and if current record is in view or not.
   *
   * @param {Core.data.Model} record Record whose row should be expanded
   * @category Common
   */
  async expand(record, fromSplit = false) {
    const me = this, { client } = me;
    if (me.disabled || me.recordStateMap.has(record) || await me.trigger("beforeExpand", { record }) === false || await client.trigger("beforeRowExpand", { record }) === false) {
      return;
    }
    let recordState;
    return new Promise((resolve) => {
      var _a5;
      recordState = {
        isCreated: false,
        renderPromiseResolver: resolve,
        record
      };
      me.recordStateMap.set(record, recordState);
      me.collapsingStateMap.delete(record);
      me._shouldScrollIntoView = true;
      me.renderRowsWithAnimation(record);
      if (!fromSplit) {
        (_a5 = client.syncSplits) == null ? void 0 : _a5.call(client, (other) => other.features.rowExpander.expand(record, true));
      }
    }).then(() => {
      var _a5, _b2, _c2, _d;
      (_b2 = me.trigger) == null ? void 0 : _b2.call(me, "expand", {
        record,
        expandedElements: recordState.expandedBodyElements,
        widget: (_a5 = recordState.widgets) == null ? void 0 : _a5[me.client.regions[0]],
        widgets: recordState.widgets
      });
      (_d = client.trigger) == null ? void 0 : _d.call(client, "rowExpand", {
        record,
        expandedElements: recordState.expandedBodyElements,
        widget: (_c2 = recordState.widgets) == null ? void 0 : _c2[me.client.regions[0]],
        widgets: recordState.widgets
      });
    });
  }
  /**
   * Tells the RowExpander that the provided record should be collapsed. If the record is in view, it will be
   * collapsed. If the record is not in view, it will simply not be expanded when rendered into view.
   *
   * @param {Core.data.Model} record Record whose row should be collapsed
   * @category Common
   */
  async collapse(record, fromSplit = false) {
    var _a5;
    const me = this, { client } = me, recordState = me.recordStateMap.get(record);
    if (me.disabled || await me.trigger("beforeCollapse", { record }) === false || await client.trigger("beforeRowCollapse", { record }) === false) {
      return;
    }
    if (recordState == null ? void 0 : recordState.expandedBodyElements) {
      for (const region in recordState.expandedBodyElements) {
        me.unobserveResize(recordState.expandedBodyElements[region]);
      }
    }
    me.recordStateMap.delete(record);
    me.collapsingStateMap.set(record, recordState);
    await me.renderRowsWithAnimation(record);
    me.trigger("collapse", { record });
    client.trigger("rowCollapse", { record });
    if (!fromSplit) {
      (_a5 = client.syncSplits) == null ? void 0 : _a5.call(client, (other) => other.features.rowExpander.collapse(record, true));
    }
  }
  /**
   * Tells the RowExpander that the provided expanded record content should be refreshed. If or when the record is
   * rendered into view, the content will be refreshed.
   *
   * Promise will resolve when the grid gets refreshed. Note that this does not mean that the provided record content
   * has been re-rendered yet, as it could be scrolled out of view.
   *
   * @param {Core.data.Model} record Record whose row should be refreshed
   * @category Common
   */
  async refreshRow(record) {
    const recordState = this.recordStateMap.get(record);
    if (recordState == null ? void 0 : recordState.isCreated) {
      recordState.isCreated = false;
      return this.bufferedRenderer(record);
    }
  }
  //endregion
  // region Nested navigation
  get hasWidget() {
    return Boolean(this.widget) || [...this.recordStateMap.values()].some((state) => state.widgets);
  }
  // Overrides the original, hence the if statement
  navigateDown() {
    if (!this.onKeyboardIn()) {
      return this.overridden.navigateDown(...arguments);
    }
  }
  // Chains the original
  navigateUp() {
    this.onKeyboardIn(true);
  }
  // Detects if focus is being reverted here by a nested grid, and focuses either the expanded row or the row below
  catchFocus({ navigationDirection, source, editing }) {
    var _a5;
    const { client } = this, { focusedCell, regions } = client;
    for (let [record, state] of this.recordStateMap.entries()) {
      for (const region in state.expandedBodyElements) {
        if (state.expandedBodyElements[region].widget === source) {
          if (editing) {
            const nextRegion = regions[regions.indexOf(region) + (navigationDirection === "down" ? 1 : -1)];
            if (nextRegion) {
              return this.startEditInWidget((_a5 = state.widgets) == null ? void 0 : _a5[nextRegion], navigationDirection === "down");
            }
          }
          if (navigationDirection === "down") {
            record = client.store.getNext(record, void 0, true);
          }
          let column;
          if (!editing && focusedCell && !focusedCell._isDefaultFocus) {
            column = focusedCell.column;
          } else {
            column = this.getNavigateableColumn(client, true, editing && navigationDirection === "up");
          }
          client._focusedCell = null;
          const cellContext = client.normalizeCellContext({ record, column });
          client.focusCell(cellContext);
          if (editing) {
            client.startEditing(cellContext);
          }
          return true;
        }
      }
    }
  }
  // Detects if the user keyboard navigates either from the expanded row and down, or the row below the expanded row
  // and up. If so, and there is a Grid in the expanded body, it starts to keyboard navigate there
  onKeyboardIn(up) {
    var _a5, _b2, _c2;
    const { focusedCell } = this.client, state = focusedCell && this.recordStateMap.get(focusedCell.record), widget = (_c2 = (_b2 = state == null ? void 0 : state.expandedBodyElements) == null ? void 0 : _b2[(_a5 = focusedCell == null ? void 0 : focusedCell.column) == null ? void 0 : _a5.region]) == null ? void 0 : _c2.widget;
    if (widget == null ? void 0 : widget.isGrid) {
      let column;
      if (widget.focusedCell && !widget.focusedCell._isDefaultFocus) {
        column = widget.focusedCell.column;
      } else {
        column = this.getNavigateableColumn(widget);
      }
      widget._focusedCell = null;
      widget.focusCell(widget.normalizeCellContext({
        record: widget[`${up ? "last" : "first"}VisibleRow`],
        column
      }));
      return true;
    }
    return false;
  }
  /**
   * Get the first column that is not the `checkboxSelectionColumn` and not the expander column.
   * @param grid
   * @param editable Also checks that the column has an `editor`
   * @param reverse If `true`, this functions returns the last column which meets the requirements
   * @private
   */
  getNavigateableColumn(grid, editable = true, reverse = false) {
    var _a5;
    const columns = reverse ? [...grid.columns.visibleColumns].reverse() : grid.columns.visibleColumns;
    return (_a5 = columns.find(
      (c) => {
        var _a6;
        return c !== grid.checkboxSelectionColumn && c !== ((_a6 = grid.features.rowExpander) == null ? void 0 : _a6._expander) && (!editable || c.editor);
      }
    )) != null ? _a5 : columns[0];
  }
  isActionAvailable({ actionName }) {
    var _a5;
    if (actions3[actionName]) {
      return !this.isDisabled && ((_a5 = this.client.features.cellEdit) == null ? void 0 : _a5.isEditing);
    }
  }
  onTab(previous) {
    var _a5;
    const { client } = this, { cellEdit } = client.features;
    if (cellEdit == null ? void 0 : cellEdit.enabled) {
      const { columns, regions } = client, { activeRecord } = cellEdit, next = previous !== true, nextCell = cellEdit.getAdjacentEditableCell(client.focusedCell, next), expandedRecord = next ? activeRecord : nextCell ? client.store.getById(nextCell.id) : null, widgets = expandedRecord && ((_a5 = this.recordStateMap.get(expandedRecord)) == null ? void 0 : _a5.widgets);
      if (widgets && (activeRecord == null ? void 0 : activeRecord.id) !== (nextCell == null ? void 0 : nextCell.id)) {
        let nextRegion = (nextCell ? columns.getById(nextCell.columnId) : columns.visibleColumns[0]).region;
        while (nextRegion) {
          const widget = widgets[nextRegion];
          if (this.startEditInWidget(widget, next)) {
            return true;
          }
          nextRegion = regions[regions.indexOf(nextRegion) + (next ? 1 : -1)];
        }
      }
    }
    return false;
  }
  startEditInWidget(widget, next = true) {
    var _a5, _b2;
    const { cellEdit } = this.client.features;
    if ((cellEdit == null ? void 0 : cellEdit.enabled) && ((_b2 = (_a5 = widget == null ? void 0 : widget.features) == null ? void 0 : _a5.cellEdit) == null ? void 0 : _b2.enabled)) {
      cellEdit.finishEditing().then(() => {
        const record = widget[`${next ? "first" : "last"}VisibleRow`], column = this.getNavigateableColumn(widget, true, !next);
        widget.startEditing(widget.normalizeCellContext({ record, column }));
      });
      return true;
    }
  }
  onShiftTab() {
    return this.onTab(true);
  }
  // endregion
  // Resize observe in own function for overridability
  observeResize(element) {
    const me = this;
    if (!me.resizeObserver) {
      me.resizeObserver = new ResizeObserver((entries) => me.onExpanderBodyResize(entries));
    }
    me.resizeObserver.observe(element);
  }
  // Unobserves an observed element
  unobserveResize(element) {
    var _a5;
    (_a5 = this.resizeObserver) == null ? void 0 : _a5.unobserve(element);
  }
  doDestroy() {
    var _a5;
    (_a5 = this.resizeObserver) == null ? void 0 : _a5.disconnect();
    delete this.resizeObserver;
    for (const [, state] of this.recordStateMap) {
      this.destroyWidgets(state);
    }
    super.doDestroy();
  }
  onThemeChange({ prev, theme }) {
    for (const [, entry] of this.recordStateMap) {
      Object.values(entry.expandedBodyElements).forEach((bodyElement) => {
        var _a5, _b2, _c2, _d;
        const shadowRootContainer = bodyElement.querySelector("." + this.shadowRootContainerClass);
        if (shadowRootContainer == null ? void 0 : shadowRootContainer._shadowRoot) {
          DomHelper.cloneStylesIntoShadowRoot(shadowRootContainer == null ? void 0 : shadowRootContainer._shadowRoot, true);
          (_b2 = (_a5 = bodyElement.widget) == null ? void 0 : _a5.element) == null ? void 0 : _b2.classList.remove(`b-theme-${prev}`);
          (_d = (_c2 = bodyElement.widget) == null ? void 0 : _c2.element) == null ? void 0 : _d.classList.add(`b-theme-${theme}`);
        }
      });
    }
  }
  /**
   * Gets the corresponding expanded record from either a nested widget or an element in the expanded body.
   * @param {HTMLElement|Core.widget.Widget} elementOrWidget
   * @returns {Core.data.Model}
   */
  getExpandedRecord(elementOrWidget) {
    var _a5;
    for (const [rec, { widgets, expandedBodyElements }] of this.recordStateMap.entries()) {
      if (widgets && elementOrWidget.isWidget) {
        for (const region in widgets) {
          if (widgets[region] === elementOrWidget) {
            return rec;
          }
        }
        if ((_a5 = elementOrWidget.expandedRecord) == null ? void 0 : _a5.isModel) {
          return elementOrWidget.expandedRecord;
        }
      } else {
        for (const region in expandedBodyElements) {
          const curEl = expandedBodyElements[region];
          if (curEl === elementOrWidget || curEl.contains(elementOrWidget)) {
            return rec;
          }
        }
      }
    }
    return null;
  }
  /**
   * Gets the expanded widget(s) for a specified record. The widget(s) will be returned as an object with region
   * names as properties and the widgets as values.
   * @param {Core.data.Model} record
   * @returns {Core.widget.Widget}
   */
  getExpandedWidgets(record) {
    var _a5;
    return (_a5 = this.recordStateMap.get(record)) == null ? void 0 : _a5.widgets;
  }
};
//region Config
__publicField(RowExpander, "$name", "RowExpander");
__publicField(RowExpander, "configurable", {
  /**
   * The implementation of this function is called each time the body of an expanded row is rendered. Either
   * return an HTML string, a {@link Core.helper.DomHelper#typedef-DomConfig} object describing the markup or any
   * Widget configuration object, like a Grid configuration object for example.
   *
   * <div class="note">
   * You should never modify any records inside this method.
   * </div>
   *
   * ```javascript
   * new Grid({
   *    features : {
   *        rowExpander : {
   *            renderer({record, region, expanderElement}){
   *                return htmlToBeExpanded;
   *            }
   *        }
   *    }
   * });
   * ```
   *
   * Or return a {@link Core.helper.DomHelper#typedef-DomConfig} object.
   *
   * ```javascript
   * new Grid({
   *    features : {
   *        rowExpander : {
   *            renderer({record, region, expanderElement}){
   *                return {
   *                   tag       : 'form',
   *                   className : 'expanded-row-form',
   *                   children  : [
   *                       {
   *                           tag        : 'textarea',
   *                           name       : 'description',
   *                           className  : 'expanded-textarea'
   *                       },
   *                       {
   *                           tag        : 'button',
   *                           text       : 'Save',
   *                           className  : 'expanded-save-button',
   *                       }
   *                   ]
   *                };
   *            }
   *        }
   *    }
   * });
   * ```
   *
   * Or return a Widget configuration object. What differs a Widget configuration object from a DomConfig object
   * is the presence of the `type` property and the absence of a `tag` property.
   *
   * ```javascript
   * new Grid({
   *    features : {
   *        rowExpander : {
   *            async renderer({record, region, expanderElement}){
   *                const myData = await fetch('myURL');
   *                return {
   *                   type : 'grid',
   *                   autoHeight : true,
   *                   columns : [
   *                       ...
   *                   ],
   *                   data : myData
   *                };
   *            }
   *        }
   *    }
   * });
   * ```
   *
   * It is also possible to add markup directly to the expanderElement.
   *
   * ```javascript
   * new Grid({
   *    features : {
   *        rowExpander : {
   *            renderer({record, region, expanderElement}){
   *                new UIComponent({
   *                    appendTo: expanderElement,
   *                    ...
   *                });
   *            }
   *        }
   *    }
   * });
   * ```
   * The renderer function can also be asynchronous.
   *
   * ```javascript
   * new Grid({
   *    features : {
   *        rowExpander : {
   *            async renderer({record, region, expanderElement}){
   *                return await awaitAsynchronousOperation();
   *            }
   *        }
   *    }
   * });
   * ```
   *
   * <div class="note">Please note that returning a Widget configuration object requires access to the Shadow DOM.
   * If your app lives in a sandboxed environment with restrictions on Shadow DOM manipulation, this is not
   * supported. Known example of such environment is the SalesForce Lightning Web Components running without
   * Lightning Web Security enabled.
   * </div>
   *
   * @config {Function}
   * @param {Object} renderData Object containing renderer parameters
   * @param {Core.data.Model} renderData.record Record for the row
   * @param {HTMLElement} renderData.expanderElement Expander body element
   * @param {HTMLElement} renderData.rowElement Row element
   * @param {String} renderData.region Grid region name
   * @param {Grid.view.Grid} renderData.grid Grid instance
   * @returns {String|DomConfig|null} Row expander body content
   * @async
   *
   * @category Rendering
   */
  renderer: null,
  /**
   * The name of the Grid event that will toggle expander. Defaults to `null` but can be set to any event such
   * as {@link Grid.view.mixin.GridElementEvents#event-cellDblClick} or
   * {@link Grid.view.mixin.GridElementEvents#event-cellClick}.
   *
   * ```javascript
   * features : {
   *     rowExpander : {
   *         triggerEvent : 'cellclick'
   *     }
   * }
   * ```
   *
   * @config {String}
   */
  triggerEvent: null,
  /**
   * Provide a column config object to display a button with expand/collapse functionality.
   * Shown by default, set to `null` to not include.
   *
   * ```javascript
   * new Grid({
   *    features : {
   *        rowExpander : {
   *            column: {
   *                // Use column config options here
   *                region: 'last'
   *            }
   *        }
   *    }
   * });
   * ```
   *
   * @config {ActionColumnConfig|Grid.column.ActionColumn}
   */
  column: {},
  /**
   * Makes the expand/collapse button column appear either as the first column (default or `first`) or as the
   * last (set to `last`). Note that the column by default will be added to the first region, if the Grid
   * has multiple regions. Use the {@link #config-column} config to change region.
   * @config {'first'|'last'}
   * @default
   */
  columnPosition: "first",
  /**
   * If set to `true`, the RowExpander will, on record update, re-render an expanded row by calling the
   * {@link #config-renderer} function or recreate the configured {@link #config-widget}.
   * @config {Boolean}
   * @default
   */
  refreshOnRecordChange: false,
  /**
   * Use this for customizing async {@link #config-renderer} loading indicator height.
   * @config {Number}
   * @defalt
   */
  loadingIndicatorHeight: 100,
  /**
   * Use this for customizing async {@link #config-renderer} loading indicator text.
   * @config {String}
   * @default Loading
   */
  loadingIndicatorText: "L{loading}",
  /**
   * Use this to disable expand and collapse animations.
   * @config {Boolean}
   * @default
   */
  enableAnimations: true,
  /**
   * A widget configuration object that will be used to create a widget to render into the row expander body. Can
   * be used instead of providing a {@link #config-renderer}.
   *
   * If the widget needs a store, it can be populated by use of the {@link #config-dataField} config. This will
   * create a store from the expanded record's corresponding `dataField` value, which needs to be an array of
   * objects or a store itself.
   *
   * ```javascript
   * new Grid({
   *    features : {
   *        rowExpander : {
   *            widget : {
   *                type : 'detailGrid',
   *            },
   *            dataField : 'orderDetails'
   *        }
   *    }
   * });
   * ```
   *
   * If there is multiple regions, you can configure each region like so:
   *
   * ```javascript
   * new Grid({
   *     features : {
   *         rowExpander : {
   *             widget : {
   *                 // The region name is the property, and its widget config the value
   *                 left : {
   *                    type : 'detailGrid',
   *                    // If your widgets uses different data sources, put the dataField
   *                    //  property in the widget configuration object
   *                    dataField : 'orderDetails'
   *                },
   *                middle : {
   *                    type : 'summaryGrid',
   *                    dataField : 'sumDetails
   *                },
   *                // No expander here
   *                right : null
   *             }
   *         }
   *     }
   * })
   * ```
   *
   * <div class="note">Please note that usage of this config requires access to the Shadow DOM. If your app lives
   * in a sandboxed environment with restrictions on Shadow DOM manipulation, this is not supported. Known
   * example of such environment is the SalesForce Lightning Web Components running without Lightning Web Security
   * enabled.
   * </div>
   *
   * @config {ContainerItemConfig}
   * @typings {ContainerItemConfig}
   */
  widget: null,
  /**
   * Used together with {@link #config-widget} to populate the widget's Store from the expanded record's
   * corresponding `dataField` value, which needs to be an array of objects or a store itself.
   * @config {String}
   */
  dataField: null,
  keyMap: {
    // Private
    Tab: { handler: "onTab", weight: 50 },
    "Shift+Tab": { handler: "onShiftTab", weight: 50 }
  },
  /**
   * When expanding a row and the expanded body element is not completely in view, setting this to `true` will
   * automatically scroll the expanded row into view.
   * @config {Boolean}
   * @default
   */
  autoScroll: false,
  /**
   * When the Grid has multiple regions, setting this config to `true` changes how the expanded content is created
   * and rendered. Instead of calling {@link #config-renderer} once per region (or one {@link #config-widget}
   * per region) it will only create one expanded element which will span the full grid width regardless of Grid
   * regions.
   * @config {Boolean}
   * @default
   */
  spanRegions: false,
  callOnFunctions: true
});
__publicField(RowExpander, "deprecatedEvents", {
  beforeExpand: {
    product: "grid",
    invalidAsOfVersion: "7.0.0",
    message: "RowExpander `beforeExpand` was replaced by the `beforeRowExpand` event on the Grid itself"
  },
  expand: {
    product: "grid",
    invalidAsOfVersion: "7.0.0",
    message: "RowExpander `expand` was replaced by the `rowExpand` event on the Grid itself"
  },
  beforeCollapse: {
    product: "grid",
    invalidAsOfVersion: "7.0.0",
    message: "RowExpander `beforeCollapse` was replaced by the `beforeRowCollapse` event on the Grid itself"
  },
  collapse: {
    product: "grid",
    invalidAsOfVersion: "7.0.0",
    message: "RowExpander `collapse` was replaced by the `rowCollapse` event on the Grid itself"
  }
});
RowExpander._$name = "RowExpander";
GridFeatureManager.registerFeature(RowExpander);

// ../Grid/lib/Grid/feature/RowReorder.js
var RowReorder = class extends Delayable_default(InstancePlugin) {
  construct(grid, config) {
    this.grid = grid;
    if (grid.isGanttBase && !("dropOnLeaf" in config)) {
      config.dropOnLeaf = true;
    }
    super.construct(...arguments);
  }
  doDestroy() {
    var _a5;
    (_a5 = this.dragHelper) == null ? void 0 : _a5.destroy();
    super.doDestroy();
  }
  /**
   * Initialize drag & drop (called on first paint)
   * @private
   */
  init() {
    const me = this, { grid, allowCrossGridDrag } = me;
    me.dragHelper = DragHelper.new({
      name: "rowReorder",
      cloneTarget: true,
      dragThreshold: 10,
      proxyTopOffset: 10,
      targetSelector: ".b-grid-row",
      lockX: !allowCrossGridDrag,
      allowDropOutside: true,
      outerElement: me.targetSubGridElement,
      touchStartDelay: me.touchStartDelay,
      isElementDraggable: me.isElementDraggable.bind(me),
      dragWithin: allowCrossGridDrag ? grid.floatRoot : grid.bodyContainer,
      scrollManager: grid.scrollManager,
      positioning: "inset",
      monitoringConfig: {
        scrollables: [
          {
            element: grid.scrollable.element,
            direction: "vertical"
          }
        ]
      },
      setXY(element, x, y) {
        const { context } = this;
        if (!context.started) {
          const elementRect = Rectangle.from(context.element, this.dragWithin), pointerDownOffset = context.startPageY - globalThis.pageYOffset - context.element.getBoundingClientRect().top;
          this._elementHeight = elementRect.height;
          y = elementRect.top + pointerDownOffset + this.proxyTopOffset;
          this._startScrollY = grid.scrollable.y;
        }
        const newY = allowCrossGridDrag ? y : Math.min(grid.scrollable.element.scrollHeight - this._elementHeight - 1, y + grid.scrollable.y - this._startScrollY);
        DomHelper.setTopInsetInlineStart(element, newY, x);
      },
      // Since parent nodes can expand after hovering, meaning original drag start position now refers to a different point in the tree
      ignoreSamePositionDrop: false,
      createProxy(element) {
        const container = document.createElement("div"), copyIcon = document.createElement("i");
        container.classList.add("b-row-reorder-proxy");
        copyIcon.className = "b-row-proxy-copy " + me.copyIconCls;
        container.appendChild(copyIcon);
        grid.selectedRecords.slice().sort((r1, r2) => grid.store.indexOf(r1) - grid.store.indexOf(r2)).forEach((record) => {
          var _a5, _b2;
          const clone = (_b2 = (_a5 = grid.rowManager.getRowFor(record)) == null ? void 0 : _a5.elements[grid.regions[0]]) == null ? void 0 : _b2.cloneNode(true);
          if (clone) {
            clone.style.transform = clone.style.insetInlineStart = clone.style.top = "";
            clone.style.width = element.offsetWidth + "px";
            clone.removeAttribute("id");
            container.appendChild(clone);
          }
        });
        if (grid.selectedRecords.length > 1) {
          const rowCountBadgeEl = document.createElement("div");
          rowCountBadgeEl.classList.add("b-badge", "b-row-drag-count-indicator");
          rowCountBadgeEl.dataset.badge = grid.selectedRecords.length;
          container.appendChild(rowCountBadgeEl);
        }
        DomHelper.removeClsGlobally(container, "b-selected", "b-hover", "b-focused");
        return container;
      },
      internalListeners: {
        beforedragstart: "onBeforeDragStart",
        dragstart: "onDragStart",
        drag: "onDrag",
        drop: "onDrop",
        abort: "onAbort",
        reset: "onReset",
        prio: 1e4,
        // To ensure our listener is run before the relayed listeners (for the outside world)
        thisObj: me
      }
    }, me.dragHelperConfig);
    grid.relayEvents(me.dragHelper, ["beforeDragStart", "dragStart", "drag"], "gridRow", true);
    me.dropIndicator = DomHelper.createElement({
      className: "b-row-drop-indicator"
    });
    me.dropOverTargetCls = ["b-row-reordering-target", "b-hover"];
  }
  //endregion
  //region Plugin config
  static get pluginConfig() {
    return {
      chain: ["beforeRenderCell", "onInternalPaint"]
    };
  }
  get targetSubGridElement() {
    const targetSubGrid = this.grid.regions[0];
    return this.grid.subGrids[targetSubGrid].element;
  }
  beforeRenderCell({ cellElement, column, record, cellCls }) {
    const { grid } = this;
    cellCls["b-row-reorder-grip"] = this.showGrip && this.enabled && !record.isSpecialRow && column === grid.columns.visibleColumns[0];
  }
  //endregion
  //region Events (drop)
  isElementDraggable(el, event) {
    var _a5;
    const hasRowResizeAndCloseToEdges = (_a5 = this.client.features.rowResize) == null ? void 0 : _a5.isEventOverResizeHandle(event);
    if (!hasRowResizeAndCloseToEdges && !el.closest(".b-grid-cell .b-widget")) {
      if (this.gripOnly) {
        const firstCell = el.closest(".b-grid-cell:first-child");
        if (firstCell) {
          const gripperStyle = getComputedStyle(firstCell, ":before"), offsetX = this.grid.rtl ? firstCell.getBoundingClientRect().width - event.borderOffsetX : event.borderOffsetX, onGrip = offsetX <= parseFloat(gripperStyle.width);
          if (onGrip) {
            this.client.preventDragSelect = true;
          }
          return onGrip;
        }
      } else {
        return true;
      }
    }
  }
  onBeforeDragStart({ event, source, context }) {
    const me = this, { grid } = me, subGridEl = me.targetSubGridElement, startRecord = context.startRecord = grid.getRecordFromElement(context.element);
    if (event.target.classList.contains("b-rowexpander-shadowroot-container") || me.disabled || grid.readOnly || // Allow dragging leaf tasks only when tree-grouped
    grid.isTreeGrouped && startRecord.isParent || !subGridEl.contains(context.element)) {
      return false;
    }
    if (startRecord.readOnly || startRecord.isSpecialRow) {
      return false;
    }
    context.originalRowTop = grid.rowManager.getRowFor(startRecord).top;
    if (!grid.selectionMode.checkboxOnly) {
      if (source.startEvent.pointerType === "touch") {
        if (!grid.isSelected(startRecord)) {
          grid.selectRow({
            record: startRecord,
            addToSelection: false
          });
        }
      } else if (!grid.isSelected(startRecord) && !event.shiftKey && !event.ctrlKey) {
        grid.selectRow({
          record: startRecord
        });
      }
    }
    const selectedRecords = grid.selectedRecords.filter((r) => !r.readOnly);
    context.records = [startRecord];
    if (selectedRecords.includes(startRecord)) {
      context.records.push(...selectedRecords.filter((r) => r !== startRecord));
      context.records.sort((r1, r2) => grid.store.indexOf(r1) - grid.store.indexOf(r2));
    }
    return true;
  }
  onDragStart({ context }) {
    var _a5, _b2;
    const me = this, { grid } = me, { cellEdit, cellMenu, headerMenu } = grid.features;
    me.targetGrid = grid;
    if (cellEdit) {
      me.cellEditDisabledState = cellEdit.disabled;
      cellEdit.disabled = true;
    }
    (_a5 = cellMenu == null ? void 0 : cellMenu.hideContextMenu) == null ? void 0 : _a5.call(cellMenu, false);
    (_b2 = headerMenu == null ? void 0 : headerMenu.hideContextMenu) == null ? void 0 : _b2.call(headerMenu, false);
    const focusedCell = context.element.querySelector(".b-focused");
    focusedCell == null ? void 0 : focusedCell.classList.remove("b-focused");
    context.element.firstElementChild.classList.remove("b-selected", "b-hover");
  }
  resolveGrid(event, context) {
    let { target } = event;
    if (/^touch/.test(event.type)) {
      const center = Rectangle.from(context.element, null, true).center;
      target = DomHelper.elementFromPoint(center.x, center.y);
    }
    return this.client.constructor.fromElement(target, (w) => w.isGridBase);
  }
  onDrag({ context, event }) {
    var _a5;
    const me = this, { clientY } = event;
    let grid = me.grid;
    if (me.allowCrossGridDrag) {
      grid = me.resolveGrid(event, context);
      me.targetGrid = grid;
      if (!grid) {
        context.valid = false;
        return;
      }
    }
    const { store, rowManager } = grid;
    let valid = true, row = rowManager.getRowAt(clientY), overRecord, dataIndex, after, over, insertBefore;
    if (row) {
      const rowTop = row.top + grid.scrollable.element.getBoundingClientRect().top - grid.scrollable.y, quarter = row.height / 4, topQuarter = rowTop + quarter, middleY = rowTop + row.height / 2, bottomQuarter = rowTop + quarter * 3;
      dataIndex = row.dataIndex;
      overRecord = store.storage.getAt(dataIndex);
      if (store.isTree) {
        over = (overRecord.isParent || me.dropOnLeaf) && clientY > topQuarter && clientY < bottomQuarter;
      } else if (store.isGrouped) {
        over = overRecord.isGroupHeader && overRecord.meta.collapsed;
      }
      after = !over && event.clientY >= middleY;
    } else {
      if (event.pageY < grid._bodyRectangle.y) {
        dataIndex = 0;
        overRecord = store.first;
        after = false;
      } else {
        dataIndex = store.count - 1;
        overRecord = store.last;
        after = true;
      }
      row = rowManager.getRow(dataIndex);
    }
    if (overRecord === me.overRecord && me.after === after && me.over === over) {
      context.valid = true;
      return;
    }
    if (me.overRecord !== overRecord) {
      (_a5 = rowManager.getRowById(me.overRecord)) == null ? void 0 : _a5.removeCls(me.dropOverTargetCls);
    }
    me.overRecord = overRecord;
    me.after = after;
    me.over = over;
    if (
      // Don't allow dropping on a readOnly grid
      me.targetGrid.readOnly || // Hovering the dragged record. This is a no-op.
      // But still gather the contextual data.
      overRecord === context.startRecord || // Not allowed to drop above topmost group header or below a collapsed header
      !after && !over && dataIndex === 0 && store.isGrouped || // Not allowed to drop after last collapsed group
      after && overRecord && overRecord.isGroupHeader && overRecord.meta.collapsed && store.indexOf(overRecord) === store.count - 1
    ) {
      valid = false;
    }
    if (store.isTree) {
      if (overRecord) {
        insertBefore = after ? overRecord.nextSibling : overRecord;
        if (context.records.some((rec) => rec.contains(overRecord))) {
          valid = false;
        }
        context.parent = valid && over ? overRecord : overRecord.parent;
        me.clearTimeout(me.hoverTimer);
        if (overRecord && overRecord.isParent && !overRecord.isExpanded(store)) {
          me.hoverTimer = me.setTimeout(() => grid.expand(overRecord), me.hoverExpandTimeout);
        }
      } else {
        context.parent = grid.store.rootNode;
      }
    } else {
      insertBefore = after ? store.storage.getAt(dataIndex + 1) : overRecord;
    }
    if (row) {
      DomHelper.setTranslateY(me.dropIndicator, Math.max(row.top + (after ? row.element.getBoundingClientRect().height : 0), 1));
      row == null ? void 0 : row.toggleCls(me.dropOverTargetCls, valid && over);
    } else if (grid.store.count === 0) {
      DomHelper.setTranslateY(me.dropIndicator, 0);
    }
    if (!over && dataIndex === store.indexOf(context.startRecord) + (after ? -1 : 1) && context.parent && context.startRecord.parent === context.parent) {
      valid = false;
    }
    me.dropIndicator.style.visibility = over ? "hidden" : "visible";
    me.dropIndicator.classList.toggle("b-drag-invalid", !valid);
    context.insertBefore = insertBefore;
    context.valid = me.reorderValid = valid;
  }
  /**
   * Handle drop
   * @private
   */
  async onDrop(event) {
    const me = this, { grid, targetGrid } = me, { context } = event, { ctrlKeyDown } = GlobalEvents_default;
    context.valid = context.valid && me.reorderValid;
    event.source = grid;
    if (context.valid) {
      context.async = true;
      if (targetGrid.store.isTree) {
        context.oldPositionContext = context.records.map((record) => {
          var _a5;
          return {
            record,
            parentId: (_a5 = record.parent) == null ? void 0 : _a5.id,
            parentIndex: record.parentIndex
          };
        });
      }
      if (await grid.trigger("gridRowBeforeDropFinalize", event) === false) {
        context.valid = false;
      }
      me.animateDrop && await me.dragHelper.animateProxyTo(me.dropIndicator, { align: "l0-l0" });
      await me.finalizeReorder(context, ctrlKeyDown);
    }
    me.clearTimeout(me.hoverTimer);
    grid.trigger("gridRowDrop", event);
    me.overRecord = me.after = me.over = me.targetGrid = null;
  }
  onAbort(event) {
    event.source = this.client;
    this.client.trigger("gridRowDragAbort", event);
    this.targetGrid = null;
  }
  async finalizeReorder(context, isCopy) {
    var _a5, _b2, _c2;
    const me = this, { client, allowCrossGridDrag } = me, targetGrid = context.targetGrid || client, { store, focusedCell } = targetGrid, { parent } = context, inSameGrid = store === client.store, record = focusedCell == null ? void 0 : focusedCell.record, focusedRecord = ((_a5 = record == null ? void 0 : record.proxyMeta) == null ? void 0 : _a5.originalRecord) || record;
    let { records, insertBefore } = context;
    if (!allowCrossGridDrag && context.valid) {
      context.valid = !records.some((rec) => !store.includes(rec));
    }
    if (context.valid) {
      const sorterFn = me.preserveSorters && store.isSorted && store.sorterFn;
      let result;
      if (isCopy) {
        records = records.map((rec) => rec.copy());
      }
      if (sorterFn) {
        records.sort(sorterFn);
      }
      if (!inSameGrid && me.allowCrossGridDrag.transferData) {
        await me.allowCrossGridDrag.transferData({
          sourceGrid: client,
          targetGrid,
          records,
          insertBefore,
          parent
        });
        targetGrid.focusCell({
          grid: targetGrid,
          record: records[0]
        });
      } else if (store.isTree) {
        records = records.filter((record2) => !record2.parent || record2.bubbleWhile((parent2) => !records.includes(parent2), true));
        if (targetGrid.isTreeGrouped) {
          if (parent !== record.parent) {
            parent.bubble((parent2) => {
              records.forEach((record2) => {
                record2[parent2.field] = parent2.key;
              });
            });
          } else {
            context.valid = false;
          }
        } else {
          result = await parent.tryInsertChild(records, me.over ? (_b2 = parent.children) == null ? void 0 : _b2[0] : insertBefore);
        }
        targetGrid.rowManager.forEach((r) => r.removeCls(me.dropOverTargetCls));
        if (!parent.isExpanded(store) && ((_c2 = parent.children) == null ? void 0 : _c2.length)) {
          targetGrid.expand(parent);
        }
        context.valid = result !== false;
      } else if (store.isGrouped && me.over) {
        if (isCopy) {
          store.insert(store.indexOf(insertBefore), records);
        } else {
          store.move(records, store.getNext(insertBefore));
        }
      } else {
        if (records.length > 1) {
          while (insertBefore && records.includes(insertBefore)) {
            insertBefore = store.getNext(insertBefore, false, true);
          }
        }
        if (inSameGrid) {
          if (isCopy) {
            store.insert(store.indexOf(insertBefore), records);
          } else {
            store.move(records, insertBefore);
          }
        } else {
          if (!isCopy) {
            client.store.remove(records);
          }
          store.insert(insertBefore ? store.indexOf(insertBefore) : store.count, records);
        }
      }
      if ((focusedCell == null ? void 0 : focusedCell._rowIndex) >= 0 && targetGrid === client) {
        targetGrid._focusedCell = null;
        targetGrid.focusCell({
          grid: client,
          record: focusedRecord,
          columnId: focusedCell.columnId
        });
      }
      let clearSorters = true;
      if (context.valid && sorterFn) {
        const firstRecord = records[0], lastRecord = records[records.length - 1], beforeFirst = firstRecord.previousSibling, afterLast = lastRecord.nextSibling;
        if ((!beforeFirst || sorterFn(beforeFirst, firstRecord) <= 0) && (!afterLast || sorterFn(lastRecord, afterLast) <= 0)) {
          clearSorters = false;
        }
      }
      if (context.valid && clearSorters) {
        store.clearSorters();
      }
    }
    context.finalize(context.valid);
    targetGrid.element.classList.remove("b-row-reordering");
  }
  /**
   * Clean up on reset
   * @private
   */
  onReset() {
    const me = this, { targetGrid, grid } = me, cellEdit = grid.features.cellEdit;
    grid.element.classList.remove("b-row-reordering");
    if (cellEdit) {
      cellEdit.disabled = me.cellEditDisabledState;
    }
    DomHelper.removeClsGlobally(
      grid.element,
      ...me.dropOverTargetCls
    );
    grid.scrollManager.stopMonitoring();
    if (targetGrid && grid !== targetGrid) {
      targetGrid.element.classList.remove("b-row-reordering");
      targetGrid.scrollManager.stopMonitoring();
      DomHelper.removeClsGlobally(
        targetGrid.element,
        ...me.dropOverTargetCls
      );
    }
  }
  //endregion
  //region Render
  onInternalPaint({ firstPaint }) {
    if (!this._initialized && this.grid.regions.length > 0) {
      this._initialized = true;
      this.init();
    }
  }
  //endregion
  updateShowGrip(show) {
    if (this.enabled) {
      this.grid.columns.visibleColumns[0].refreshCells();
    }
  }
  updateDisabled(disabled, was) {
    super.updateDisabled(disabled, was);
    if (this.dragHelper) {
      this.dragHelper.disabled = disabled;
    }
    if (this.showGrip) {
      this.grid.columns.visibleColumns[0].refreshCells();
    }
  }
  updateTargetGrid(targetGrid, was) {
    if (was) {
      was.element.classList.remove("b-row-reordering");
      was.scrollManager.stopMonitoring();
    }
    if (this.dragHelper.isDragging) {
      this.dragHelper.context.targetGrid = targetGrid;
    }
    if (targetGrid) {
      targetGrid.element.classList.add("b-row-reordering");
      targetGrid.bodyContainer.appendChild(this.dropIndicator);
      targetGrid.scrollManager.startMonitoring({
        scrollables: [
          {
            element: targetGrid.scrollable.element,
            direction: "vertical"
          }
        ]
      });
    } else {
      this.dropIndicator.remove();
    }
  }
  get isDragging() {
    return this.dragHelper.isDragging;
  }
};
//region Events
/**
 * Fired before dragging starts, return false to prevent the drag operation.
 * @preventable
 * @event gridRowBeforeDragStart
 * @param {Grid.view.GridBase} source
 * @param {Object} context
 * @param {Core.data.Model[]} context.records The dragged row records
 * @param {MouseEvent|TouchEvent} event
 * @on-owner
 */
/**
 * Fired when dragging starts.
 * @event gridRowDragStart
 * @param {Grid.view.GridBase} source
 * @param {Object} context
 * @param {Core.data.Model[]} context.records The dragged row records
 * @param {MouseEvent|TouchEvent} event
 * @on-owner
 */
/**
 * Fired while the row is being dragged, in the listener function you have access to `context.insertBefore` a grid /
 * tree record, and additionally `context.parent` (a TreeNode) for trees. You can signal that the drop position is
 * valid or invalid by setting `context.valid = false;`
 * @event gridRowDrag
 * @param {Grid.view.GridBase} source
 * @param {Object} context
 * @param {Boolean} context.valid Set this to true or false to indicate whether the drop position is valid.
 * @param {Core.data.Model} context.insertBefore The record to insert before (`null` if inserting at last position of a parent node)
 * @param {Core.data.Model} context.parent The parent record of the current drop position (only applicable for trees)
 * @param {Core.data.Model[]} context.records The dragged row records
 * @param {MouseEvent} event
 * @on-owner
 */
/**
 * Fired before the row drop operation is finalized. You can return false to abort the drop operation, or a
 * Promise yielding `true` / `false` which allows for asynchronous abort (e.g. first show user a confirmation dialog).
 * @event gridRowBeforeDropFinalize
 * @preventable
 * @async
 * @param {Grid.view.GridBase} source
 * @param {Object} context
 * @param {Boolean} context.valid Set this to true or false to indicate whether the drop position is valid
 * @param {Core.data.Model} context.insertBefore The record to insert before (`null` if inserting at last position of a parent node)
 * @param {Core.data.Model} context.parent The parent record of the current drop position (only applicable for trees)
 * @param {Core.data.Model[]} context.records The dragged row records
 * @param {RecordPositionContext[]} context.oldPositionContext An array of objects with information about the previous tree position.
 * Objects contain the `record`, and its original `parentIndex` and `parentId` values
 * @param {MouseEvent} event
 * @on-owner
 */
/**
 * Fired after the row drop operation has completed, regardless of validity
 * @event gridRowDrop
 * @param {Grid.view.GridBase} source
 * @param {Object} context
 * @param {Boolean} context.valid true or false depending on whether the drop position was valid
 * @param {Core.data.Model} context.insertBefore The record to insert before (`null` if inserting at last position of a parent node)
 * @param {Core.data.Model} context.parent The parent record of the current drop position (only applicable for trees)
 * @param {Core.data.Model[]} context.records The dragged row records
 * @param {RecordPositionContext[]} context.oldPositionContext An array of objects with information about the previous tree position.
 * Objects contain the record, and its original `parentIndex` and `parentId` values
 * @param {MouseEvent} event
 * @on-owner
 */
/**
 * Fired when a row drag operation is aborted
 * @event gridRowDragAbort
 * @param {Grid.view.GridBase} source
 * @param {Object} context
 * @param {Core.data.Model[]} context.records The dragged row records
 * @param {MouseEvent} event
 * @on-owner
 */
//endregion
//region Init
__publicField(RowReorder, "$name", "RowReorder");
__publicField(RowReorder, "configurable", {
  /**
   * Set to `true` to show a grip icon on the left side of each row.
   * @config {Boolean}
   */
  showGrip: null,
  /**
   * Set to `true` to only allow reordering by the {@link #config-showGrip} config.
   * @config {Boolean}
   */
  gripOnly: null,
  /**
   * If hovering over a parent node for this period of a time in a tree, the node will expand.
   * @config {Number}
   */
  hoverExpandTimeout: 1e3,
  /**
   * The amount of milliseconds to wait after a touchstart, before a drag gesture will be allowed to start.
   * @config {Number}
   * @default
   */
  touchStartDelay: 300,
  /**
   * Enables creation of parents by dragging a row and dropping it onto a leaf row. Only works in a Grid with
   * a tree store. This option is `true` by default in the Gantt product.
   * @config {Boolean}
   */
  dropOnLeaf: null,
  /**
   * The CSS class to add to the icon element indicating it is a copy operation.
   * @config {String}
   */
  copyIconCls: "b-icon b-icon-add",
  /**
   * Enables dragging rows to other grid instances. By default, this will remove from the source grid `store` and
   * add the dragged record(s) to the target grid `store`. If you would like to implement another transfer of the
   * data (e.g. to copy instead of move), you can provide an object with a `transferData` method.
   *
   * ```javascript
   * features : {
   *     rowReorder : {
   *          allowCrossGridDrag : {
   *              async transferData({ records, insertBefore, parent }) {
   *                  records = records.map((rec) => rec.copy());
   *                  await parent.insertChild(records, this.over ? parent.children?.[0] : insertBefore);
   *              }
   *         }
   *     }
   * }
   * ```
   *
   * @config {Boolean|Object} allowCrossGridDrag
   * @property {Function} allowCrossGridDrag.transferData A function which decides what should happen to the
   * dragged records upon drop. It receives a single context object with information about the drag drop state.
   * @property {Grid.view.GridBase} allowCrossGridDrag.transferData.sourceGrid The source grid
   * @property {Grid.view.GridBase} allowCrossGridDrag.transferData.targetGrid the target grid
   * @property {Core.data.Model[]} allowCrossGridDrag.transferData.records The dragged records
   * @property {Core.data.Model} allowCrossGridDrag.transferData.insertBefore The record to insert before
   * @property {Core.data.Model} allowCrossGridDrag.transferData.parent The parent record of the current drop position (only applicable for trees)
   */
  allowCrossGridDrag: false,
  /**
   * An object used to configure the internal {@link Core.helper.DragHelper} class
   * @config {DragHelperConfig}
   */
  dragHelperConfig: null,
  /**
   * Set to `true` to preserve sorters after a drop operation, if that operation leads to the store still being
   * sorted.
   * @config {Boolean}
   * @default false
   */
  preserveSorters: null,
  // Can be used in tests to disable drop animation to speed them up
  animateDrop: true,
  targetGrid: null
});
RowReorder.featureClass = "";
RowReorder._$name = "RowReorder";
GridFeatureManager.registerFeature(RowReorder, false);

// ../Grid/lib/Grid/feature/RowResize.js
var RowResize = class extends InstancePlugin {
  //region Init
  construct(grid, config) {
    const me = this;
    super.construct(...arguments);
    me.resizer = new ResizeHelper({
      name: "rowResize",
      targetSelector: ".b-grid-row:not(.b-group-header)",
      handleContainerSelector: ".b-grid-row",
      outerElement: grid.element,
      direction: "vertical",
      dragThreshold: 1,
      handleSize: Math.min(5, grid.rowHeight * 0.1),
      internalListeners: {
        beforeresizestart: me.onBeforeResizeStart,
        resizestart: me.onResizeStart,
        resizing: me.onResizing,
        resize: me.onResize,
        cancel: me.onCancel,
        thisObj: me
      },
      allowResize(target, event) {
        var _a5;
        return (_a5 = grid.hoveredCell) == null ? void 0 : _a5.matches(me.cellSelector);
      },
      overTopHandle(event, target) {
        if (this.canResize(target, event) && target.dataset.index !== "0") {
          const topHandle = Rectangle.from(target);
          topHandle.height = this.handleSize;
          return topHandle.height > 0 && Math.abs(topHandle.top - EventHelper.getPagePoint(event).top) < this.handleSize;
        }
        return false;
      },
      internalBeforeStart(context) {
        const { edge, element } = context;
        if (edge === "top") {
          return {
            edge: "bottom",
            element: element.previousElementSibling
          };
        }
      },
      // Prevent selection as a result of mousedown on virtual handle
      onResizeHandlePointerDown(event) {
        grid.selectionDisabled = true;
        if (grid.features.rowReorder) {
          me._oldReorderDisabled = grid.features.rowReorder.dragHelper.disabled;
          grid.features.rowReorder.dragHelper.disabled = true;
        }
        grid.setTimeout(() => {
          grid.selectionDisabled = false;
        }, 10);
      }
    });
  }
  doDestroy() {
    var _a5;
    (_a5 = this.resizer) == null ? void 0 : _a5.destroy();
    super.doDestroy();
  }
  //endregion
  //region Events
  onBeforeResizeStart() {
    return !this.disabled;
  }
  onResizeStart({ source, context }) {
    const { client } = this, applyGlobally = this.applyToAllRows || client.fixedRowHeight;
    context.record = client.store.getById(context.element.dataset.id);
    context.oldHeight = context.record.rowHeight;
    client.store.suspendAutoCommit();
    Object.assign(source, {
      minHeight: this.minHeight,
      maxHeight: this.maxHeight,
      // Only update live element if applying to all rows
      skipUpdatingElement: !applyGlobally
    });
    client.element.classList.add("b-row-resizing");
  }
  onResizing({ context }) {
    if (!this.applyToAllRows && !this.client.fixedRowHeight && context.valid) {
      context.record.rowHeight = context.newHeight;
    }
  }
  onResize({ context }) {
    const { client } = this;
    if (this.applyToAllRows || client.fixedRowHeight) {
      client.rowHeight = context.newHeight;
    } else if (client.selectionMode.multiSelect && client.selectedRecords.includes(context.record)) {
      client.selectedRecords.forEach((record) => record.rowHeight = context.newHeight);
    }
    this.cleanup();
  }
  /**
   * Restore row size on cancel (ESC)
   * @private
   */
  onCancel({ context }) {
    if (!this.applyToAllRows && !this.client.fixedRowHeight) {
      context.record.rowHeight = context.oldHeight;
    }
    this.cleanup();
  }
  //endregion
  cleanup() {
    const { client } = this;
    client.element.classList.remove("b-row-resizing");
    client.store.resumeAutoCommit();
    if (typeof this._oldReorderDisabled === "boolean") {
      client.features.rowReorder.dragHelper.disabled = this._oldReorderDisabled;
    }
  }
  isEventOverResizeHandle(event) {
    return this.enabled && this.resizer.checkResizeHandles(event);
  }
};
__publicField(RowResize, "$name", "RowResize");
__publicField(RowResize, "configurable", {
  /**
   * Set this to true to modify the global {@link Grid/view/Grid#config-rowHeight} which affects all grid rows.
   * @prp {Boolean}
   * @default false
   */
  applyToAllRows: null,
  /**
   * Set this to a CSS selector to only trigger row resizing in cells for a specific column.
   * @config {String}
   * @default
   */
  cellSelector: ".b-grid-cell",
  /**
   * Minimum height when resizing
   * @prp {Number}
   * @default
   */
  minHeight: 20,
  /**
   * Max height when resizing
   * @prp {Number}
   */
  maxHeight: 0
});
RowResize._$name = "RowResize";
GridFeatureManager.registerFeature(RowResize, false);

// ../Grid/lib/Grid/feature/Search.js
var actions4 = {
  goToNextHit: 1,
  goToPrevHit: 1
};
var Search = class extends Delayable_default(InstancePlugin) {
  static get properties() {
    return {
      hitCls: "b-search-hit",
      hitCellCls: "b-search-hit-cell",
      hitCellBadgeCls: "b-search-hit-cell-badge",
      hitTextCls: "b-search-hit-text"
    };
  }
  construct(grid, config) {
    super.construct(grid, config);
    Object.assign(this, {
      grid,
      text: "",
      hitEls: [],
      treeWalker: grid.setupTreeWalker(grid.element, DomHelper.NodeFilter.SHOW_TEXT, () => DomHelper.NodeFilter.FILTER_ACCEPT)
    });
    grid.ion({
      expandNode: "onTreeNodeExpand",
      thisObj: this
    });
  }
  isActionAvailable({ actionName }) {
    if (actions4[actionName]) {
      return Boolean(this.text);
    }
  }
  onTreeNodeExpand() {
    if (this.text) {
      this.requestAnimationFrame(this.search, [this.text, false, true, void 0, void 0, false]);
    }
  }
  doDestroy() {
    this.clear(true);
    super.doDestroy();
  }
  doDisable(disable) {
    if (disable) {
      this.clear();
    }
    super.doDisable(disable);
  }
  get store() {
    return this.grid.store;
  }
  //endregion
  //region Plugin config
  // Plugin configuration. This plugin chains some of the functions in Grid.
  static get pluginConfig() {
    return {
      chain: ["populateCellMenu"]
    };
  }
  //endregion
  //region Search
  /**
   * Performs a search and highlights hits.
   * @param {String} text Text to search for
   * @param {Boolean} [gotoHit] Go to first hit after search
   * @param {Boolean} [reapply] Pass true to force search
   * @param {String[]} [fields] An array of the fields to search for the value in
   */
  async search(text, gotoHit = true, reapply = false, fields, fromSplit = false, expandToHits = this.client.store.isTree) {
    var _a5;
    const me = this;
    if (!text) {
      return me.clear();
    }
    if (!reapply && text === me.text || me.disabled) {
      return;
    }
    const { grid, store } = me, columns = grid.columns.visibleColumns.filter((col) => col.searchable !== false), formatters = [];
    fields = fields || columns.map((col) => {
      var _a6;
      formatters.push((_a6 = col.formatValue) == null ? void 0 : _a6.bind(col));
      return col.field;
    });
    const found = store.search(text, fields, formatters);
    if (expandToHits && found.length) {
      await grid.expandTo(found.map((hit) => hit.id));
    }
    for (const column of columns) {
      if (column.mergeCells && column.isSorted) {
        let prevValue = null, belongsTo = null;
        for (const hit of found) {
          if (hit.field === column.field) {
            const value2 = hit.data[hit.field];
            if (value2 === prevValue) {
              hit.belongsTo = belongsTo;
            }
            prevValue = value2;
            belongsTo = `${hit.field}-${hit.id}`;
          }
        }
      }
    }
    let i = 1;
    Object.assign(me, {
      foundMap: {},
      prevFound: me.found,
      found,
      text,
      findRe: new RegExp(`(\\s+)?(${StringHelper.escapeRegExp(String(text))})(\\s+)?`, "ig")
    });
    me.clearHits();
    if (!found) {
      return;
    }
    for (const hit of found) {
      me.foundMap[`${hit.field}-${hit.id}`] = hit.belongsTo ? me.foundMap[hit.belongsTo] : i++;
      if (i > me.limit) {
        break;
      }
    }
    if (!me.listenersInitialized) {
      grid.rowManager.ion({
        name: "renderCell",
        renderCell: "renderCell",
        thisObj: me
      });
      store.ion({
        name: "storeRefresh",
        [`refresh${grid.asyncEventSuffix}`]: "onStoreRefresh",
        thisObj: me
      });
      me.listenersInitialized = true;
    }
    grid.refreshRows();
    grid.trigger("search", { grid, find: text, found });
    if (gotoHit && !me.isHitFocused && !fromSplit) {
      me.gotoNextHit(true);
    }
    (_a5 = grid.syncSplits) == null ? void 0 : _a5.call(grid, (other) => other.features.search.search(text, gotoHit, reapply, fields, true));
    return found;
  }
  clearHits() {
    if (!this.isDestroying) {
      for (const cellElement of DomHelper.children(this.grid.element, "." + this.hitCls)) {
        cellElement.classList.remove(this.hitCls, this.hitCellCls);
        const row = DomDataStore.get(cellElement).row;
        if (row) {
          row.forceInnerHTML = true;
          row.renderCell(cellElement);
          row.forceInnerHTML = false;
        }
      }
    }
  }
  /**
   * Clears search results.
   */
  clear(silent = false) {
    var _a5;
    const me = this, { grid } = me;
    if (me.foundMap) {
      delete me.foundMap;
    }
    me.text = null;
    me.clearHits();
    if (me.listenersInitialized) {
      me.detachListeners("renderCell");
      me.detachListeners("storeRefresh");
      me.listenersInitialized = false;
    }
    if (!silent) {
      grid.trigger("clearSearch", { grid });
      (_a5 = grid.syncSplits) == null ? void 0 : _a5.call(grid, (other) => other.features.search.clear());
    }
  }
  /**
   * Number of results found
   * @readonly
   * @property {Number}
   */
  get foundCount() {
    var _a5, _b2;
    return (_b2 = (_a5 = this.found) == null ? void 0 : _a5.length) != null ? _b2 : 0;
  }
  //endregion
  //region Navigation
  /**
   * Returns true if focused row is a hit
   * @property {Boolean}
   * @readonly
   */
  get isHitFocused() {
    var _a5;
    const me = this, { grid } = me, { focusedCell } = grid;
    if ((_a5 = focusedCell == null ? void 0 : focusedCell.cell) == null ? void 0 : _a5.contains(DomHelper.getActiveElement(grid.element))) {
      const { rowIndex, column } = focusedCell;
      return rowIndex !== -1 && me.found.some(
        (hit) => hit.index === rowIndex && column && hit.field === column.field
      );
    }
  }
  /**
   * Select the next hit, scrolling it into view. Triggered with [f3] or [ctrl]/[cmd] + [g].
   */
  gotoNextHit(fromStart = false) {
    var _a5;
    const me = this;
    if (!((_a5 = me.found) == null ? void 0 : _a5.length)) {
      return;
    }
    const { grid, store } = me, fromCell = grid.focusedCell || grid.lastFocusedCell, currentIndex = fromCell && fromStart !== true ? store.indexOf(fromCell.record, void 0, true) : -1, nextHit = me.found.findIndex((hit) => hit.index > currentIndex);
    if (nextHit !== -1) {
      me.gotoHit(nextHit);
    }
  }
  /**
   * Select the previous hit, scrolling it into view. Triggered with [shift] + [f3] or [shift] + [ctrl]/[cmd] + [g].
   */
  gotoPrevHit() {
    var _a5;
    const me = this, { store } = me;
    if (!((_a5 = me.found) == null ? void 0 : _a5.length)) {
      return;
    }
    const { grid, found } = me, fromCell = grid.focusedCell || grid.lastFocusedCell, currentIndex = fromCell ? store.indexOf(fromCell.record, void 0, true) : 0;
    for (let i = found.length - 1; i--; i >= 0) {
      const hit = found[i];
      if (hit.index < currentIndex) {
        me.gotoHit(i);
        break;
      }
    }
  }
  /**
   * Go to specified hit.
   * @param {Number} index
   */
  gotoHit(index) {
    const { grid } = this, nextHit = this.found[index];
    if (nextHit) {
      grid.focusCell({
        field: nextHit.field,
        id: nextHit.id
      });
    }
    return Boolean(nextHit);
  }
  /**
   * Go to the first hit.
   */
  gotoFirstHit() {
    this.gotoHit(0);
  }
  /**
   * Go to the last hit.
   */
  gotoLastHit() {
    this.gotoHit(this.found.length - 1);
  }
  //endregion
  //region Render
  /**
   * Called from SubGrid when a cell is rendered. Highlights search hits.
   * @private
   */
  renderCell({ cellElement, column, record, value: value2 }) {
    var _a5, _b2;
    const me = this, {
      treeWalker,
      findRe,
      hitTextCls,
      showHitIndex
    } = me, hitIndex = (_a5 = me.foundMap) == null ? void 0 : _a5[column.field + "-" + record.id];
    if (hitIndex) {
      cellElement.classList.add(me.hitCls);
      showHitIndex && ((_b2 = cellElement.querySelector(`.${me.hitCellBadgeCls}`)) == null ? void 0 : _b2.remove());
      const inner = DomHelper.down(cellElement, ".b-grid-cell-value,.b-tree-cell-value") || cellElement;
      if (String(value2).toLowerCase() === String(me.text).toLowerCase()) {
        inner.innerHTML = `<span class="${me.hitTextCls}">${inner.innerHTML}</span>${showHitIndex ? `<div class="${me.hitCellBadgeCls}">${hitIndex}</div>` : ""}`;
      } else {
        treeWalker.currentNode = inner;
        for (let textNode = treeWalker.nextNode(); textNode && inner.contains(textNode); ) {
          const nodeToReplace = textNode, textContent = textNode.nodeValue, newText = ["<span>"];
          textNode = treeWalker.nextNode();
          let offset = findRe.lastIndex;
          for (let match = findRe.exec(textContent); match; match = findRe.exec(textContent)) {
            const preamble = textContent.substring(offset, match.index), spaceBefore = match[1] ? "\xA0" : "", v = match[2], spaceAfter = match[3] ? "\xA0" : "";
            newText.push(`${StringHelper.encodeHtml(preamble)}${spaceBefore}<span class="${hitTextCls}">${StringHelper.encodeHtml(v)}</span>${spaceAfter}`);
            offset = findRe.lastIndex;
          }
          newText.push(StringHelper.encodeHtml(textContent.substring(offset)), "<span>");
          nodeToReplace.parentNode.insertBefore(DomHelper.createElementFromTemplate(newText.join(""), {
            fragment: true
          }), nodeToReplace);
          nodeToReplace.remove();
        }
        if (showHitIndex) {
          DomHelper.createElement({
            parent: cellElement,
            className: me.hitCellBadgeCls,
            text: hitIndex
          });
        }
      }
      me.hitEls.push(cellElement);
    }
  }
  //endregion
  //region Context menu
  /**
   * Add search menu item to cell context menu.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Core.data.Model} options.record Record for which the menu will be shown
   * @param {Object<String,MenuItemConfig|Boolean|null>} options.items A named object to describe menu items
   * @internal
   */
  populateCellMenu({ column, record, items: items2, cellElement }) {
    const me = this;
    if (column.searchable) {
      items2.search = {
        text: "L{searchForValue}",
        localeClass: me,
        icon: "b-fw-icon b-icon-search",
        cls: "b-separator",
        weight: 200,
        disabled: me.disabled,
        onItem: () => {
          let sel = globalThis.getSelection().toString();
          if (!sel) {
            sel = cellElement.innerText;
          }
          me.search(sel);
        }
      };
    }
  }
  //endregion
  //region Events
  onStoreRefresh() {
    this.search(this.text, false, true);
  }
  //endregion
};
//region Init
__publicField(Search, "$name", "Search");
__publicField(Search, "configurable", {
  /**
   * The maximum amount of search hits
   * @config {Number}
   * @default
   */
  limit: 1e3,
  /**
   * Set to `false` to not show the search hit index numbers
   * @config {Boolean}
   * @default
   */
  showHitIndex: true,
  /**
   * See {@link #keyboard-shortcuts Keyboard shortcuts} for details
   * @config {Object<String,KeyMapConfig>}
   */
  keyMap: {
    F3: "gotoNextHit",
    "Ctrl+g": "gotoNextHit",
    "Shift+F3": "gotoPrevHit",
    "Ctrl+Shift+g": "gotoPrevHit"
  }
});
Search.featureClass = "b-search";
Search._$name = "Search";
GridFeatureManager.registerFeature(Search);

// ../Grid/lib/Grid/feature/Sort.js
var emptyArray10 = Object.freeze([]);
var Sort = class extends InstancePlugin {
  static get properties() {
    return {
      ignoreRe: new RegExp([
        // Stop this feature from having to know the internals of two other optional features.
        "b-grid-header-resize-handle",
        "b-filter-icon",
        "b-widget"
      ].join("|")),
      sortableCls: "b-sortable",
      sortedCls: "b-sort",
      sortedAscCls: "b-asc",
      sortedDescCls: "b-desc"
    };
  }
  //endregion
  //region Init
  construct(grid, config) {
    config = this.processConfig(config);
    super.construct(grid, config);
    this.bindStore(this.store);
  }
  // Sort feature handles special config cases, where user can supply a string or an array of sorters
  // instead of a normal config object
  processConfig(config) {
    if (typeof config === "string" || Array.isArray(config)) {
      return {
        field: config,
        ascending: null
      };
    }
    return config;
  }
  // override setConfig to process config before applying it
  setConfig(config) {
    super.setConfig(this.processConfig(config));
  }
  bindStore(store) {
    this.detachListeners("store");
    store.ion({
      name: "store",
      beforeSort: "onStoreBeforeSort",
      sort: "syncHeaderSortState",
      thisObj: this
    });
    if (this.client.isPainted) {
      this.syncHeaderSortState();
    }
  }
  set field(field) {
    var _a5;
    const column = this.client.columns.get(field);
    if (column && typeof column.sortable === "object") {
      column.sortable.field = column.sortable.property || field;
      field = column.sortable;
    }
    if (!((_a5 = this.store.sorters) == null ? void 0 : _a5.some((g) => g.field === field))) {
      this.store.sort(field, this.ascending);
    }
  }
  // Avoid caching store, it might change
  get store() {
    return this.client[this.client.sortFeatureStore];
  }
  //endregion
  //region Plugin config
  // Plugin configuration. This plugin chains some of the functions in Grid.
  static get pluginConfig() {
    return {
      chain: ["onElementClick", "populateHeaderMenu", "getColumnDragToolbarItems", "renderHeader", "onInternalPaint", "bindStore"]
    };
  }
  //endregion
  //region Headers
  /**
   * Update headers to match stores sorters (displays sort icon in correct direction on them)
   * @private
   */
  syncHeaderSortState() {
    var _a5, _b2;
    const me = this, { client } = me;
    if (!client.hideHeaders && client.isPainted) {
      const sorters = (_a5 = me.store.sorters) != null ? _a5 : emptyArray10, sorterCount = sorters.length, classList = new DomClassList();
      let sorter;
      for (const leafColumn of client.columns.visibleColumns) {
        if (!leafColumn.sortable) {
          continue;
        }
        const leafHeader = leafColumn.element, textEl = leafColumn.textWrapper, dataset = textEl == null ? void 0 : textEl.dataset;
        let sortDirection = "none";
        (dataset == null ? void 0 : dataset.sortIndex) && delete dataset.sortIndex;
        classList.value = leafHeader.classList;
        if (leafColumn.sortable === false) {
          classList.remove(me.sortableCls);
          (_b2 = textEl == null ? void 0 : textEl.querySelector(".b-sort-icon")) == null ? void 0 : _b2.remove();
        } else {
          if (!(textEl == null ? void 0 : textEl.querySelector(".b-sort-icon"))) {
            DomHelper.createElement({
              tag: "i",
              parent: textEl,
              className: "b-icon b-sort-icon"
            });
          }
          classList.add(me.sortableCls);
          sorter = sorters.find(
            (sort) => sort.field === leafColumn.field || sort.sortFn && sort.sortFn === leafColumn.sortable.sortFn
          );
          if (sorter) {
            if (sorterCount > 1 && dataset) {
              dataset.sortIndex = sorters.indexOf(sorter) + 1;
            }
            classList.add(me.sortedCls);
            if (sorter.ascending) {
              classList.add(me.sortedAscCls);
              classList.remove(me.sortedDescCls);
              sortDirection = "ascending";
            } else {
              classList.add(me.sortedDescCls);
              classList.remove(me.sortedAscCls);
              sortDirection = "descending";
            }
          } else {
            classList.remove(me.sortedCls);
            if (!classList["b-group"]) {
              classList.remove(me.sortedAscCls);
              classList.remove(me.sortedDescCls);
            }
          }
        }
        DomHelper.syncClassList(leafHeader, classList);
        DomHelper.setAttributes(leafHeader, {
          "aria-sort": sortDirection
        });
      }
    }
  }
  updateShowIconOnColumnHover(showIconOnColumnHover) {
    this.client.element.classList.toggle("b-sort-hover-icon", Boolean(showIconOnColumnHover));
  }
  //endregion
  //region Context menu
  /**
   * Adds sort menu items to header context menu.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Object<String,MenuItemConfig|Boolean|null>} options.items A named object to describe menu items
   * @internal
   */
  populateHeaderMenu({ column, items: items2 }) {
    var _a5;
    const me = this, { store } = me, sortBy = { ...column.sortable, field: column.field, columnOwned: true }, grouper = (_a5 = store.groupers) == null ? void 0 : _a5.find((g) => g.field === column.field);
    if (column.sortable !== false) {
      items2.sortAsc = {
        text: "L{sortAscending}",
        localeClass: me,
        icon: "b-fw-icon b-icon-sort-asc",
        cls: "b-separator",
        weight: 300,
        disabled: grouper || me.disabled,
        onItem: () => store.sort(sortBy, true)
      };
      items2.sortDesc = {
        text: "L{sortDescending}",
        localeClass: me,
        icon: "b-fw-icon b-icon-sort-desc",
        weight: 310,
        disabled: grouper || me.disabled,
        onItem: () => store.sort(sortBy, false)
      };
      if (me.multiSort && me.client.columns.records.some((col) => col.sortable)) {
        const sorter = store.sorters.find((s) => s.field === column.field || column.sortable.sortFn && column.sortable.sortFn === s.sortFn);
        items2.multiSort = {
          text: "L{multiSort}",
          localeClass: me,
          icon: "b-fw-icon b-icon-sort",
          weight: 320,
          disabled: grouper || me.disabled,
          menu: {
            addSortAsc: {
              text: sorter ? "L{toggleSortAscending}" : "L{addSortAscending}",
              localeClass: me,
              icon: "b-fw-icon b-icon-sort-asc",
              disabled: sorter && (sorter == null ? void 0 : sorter.ascending),
              weight: 330,
              onItem: () => store.addSorter(sortBy, true)
            },
            addSortDesc: {
              text: sorter ? "L{toggleSortDescending}" : "L{addSortDescending}",
              localeClass: me,
              icon: "b-fw-icon b-icon-sort-desc",
              disabled: sorter && !sorter.ascending,
              weight: 340,
              onItem: () => store.addSorter(sortBy, false)
            },
            removeSorter: {
              text: "L{removeSorter}",
              localeClass: me,
              icon: "b-fw-icon b-icon-remove",
              weight: 350,
              disabled: !sorter,
              onItem: () => {
                store.removeSorter(sortBy.field);
              }
            }
          }
        };
      }
    }
  }
  /**
   * Supply items to ColumnDragToolbar
   * @private
   */
  getColumnDragToolbarItems(column, items2) {
    const me = this, { store, disabled } = me;
    if (column.sortable !== false) {
      const sorter = store.sorters.find((s) => s.field === column.field);
      items2.push(
        {
          text: "L{sortAscendingShort}",
          group: "L{sort}",
          localeClass: me,
          icon: "b-icon b-icon-sort-asc",
          ref: "sortAsc",
          cls: "b-separator",
          weight: 105,
          disabled,
          onDrop: ({ column: column2 }) => store.sort(column2.field, true)
        },
        {
          text: "L{sortDescendingShort}",
          group: "L{sort}",
          localeClass: me,
          icon: "b-icon b-icon-sort-desc",
          ref: "sortDesc",
          weight: 105,
          disabled,
          onDrop: ({ column: column2 }) => store.sort(column2.field, false)
        },
        {
          text: "L{addSortAscendingShort}",
          group: "L{multiSort}",
          localeClass: me,
          icon: "b-icon b-icon-sort-asc",
          ref: "multisortAddAsc",
          disabled: disabled || sorter && sorter.ascending,
          weight: 105,
          onDrop: ({ column: column2 }) => store.addSorter(column2.field, true)
        },
        {
          text: "L{addSortDescendingShort}",
          group: "L{multiSort}",
          localeClass: me,
          icon: "b-icon b-icon-sort-desc",
          ref: "multisortAddDesc",
          disabled: disabled || sorter && !sorter.ascending,
          weight: 105,
          onDrop: ({ column: column2 }) => store.addSorter(column2.field, false)
        },
        {
          text: "L{removeSorterShort}",
          group: "L{multiSort}",
          localeClass: me,
          icon: "b-icon b-icon-remove",
          ref: "multisortRemove",
          weight: 105,
          disabled: disabled || !sorter,
          onDrop: ({ column: column2 }) => store.removeSorter(column2.field)
        }
      );
    }
    return items2;
  }
  //endregion
  //region Events
  // Intercept sorting by a column that has a custom sorting fn, and inject that fn
  onStoreBeforeSort({ sorters }) {
    var _a5;
    const { columns } = this.client;
    for (let i = 0; i < sorters.length; i++) {
      const sorter = sorters[i], column = (sorter.columnOwned || this.prioritizeColumns) && columns.get(sorter.field);
      if ((_a5 = column == null ? void 0 : column.sortable) == null ? void 0 : _a5.sortFn) {
        sorters[i] = { ...sorter, ...column.sortable, columnOwned: true };
      }
    }
  }
  /**
   * Clicked on header, sort Store.
   * @private
   */
  onElementClick(event) {
    const me = this, { store } = me, { target } = event, header = target.closest(".b-grid-header.b-sortable"), field = header == null ? void 0 : header.dataset.column;
    if (me.ignoreRe.test(target.className) || target.closest(".b-grid-headerwidget") || me.disabled || event.handled) {
      return;
    }
    if (header && field && (me.toggleOnHeaderClick || target.closest(".b-sort-icon"))) {
      const column = me.client.columns.getById(header.dataset.columnId), columnGrouper = store.isGrouped && store.groupers.find((g) => g.field === field);
      if (columnGrouper && !event.shiftKey) {
        return;
      }
      if (column.sortable && !event.shiftKey) {
        if (event.ctrlKey && event.altKey) {
          store.removeSorter(column.field);
        } else {
          const sortBy = {
            columnOwned: true,
            field: column.field
          };
          if (typeof column.sortable === "object") {
            ObjectHelper.assign(sortBy, column.sortable);
          }
          store.sort(sortBy, null, event.ctrlKey && me.multiSort);
        }
      }
    }
  }
  /**
   * Called when grid headers are rendered, make headers match current sorters.
   * @private
   */
  renderHeader() {
    this.syncHeaderSortState();
  }
  onInternalPaint() {
    this.syncHeaderSortState();
  }
  //endregion
};
//region Config
__publicField(Sort, "$name", "Sort");
__publicField(Sort, "configurable", {
  /**
   * Enable multi sort
   * @config {Boolean}
   * @default
   */
  multiSort: true,
  /**
   * Use custom sorting functions defined on columns also when programmatically sorting by the columns field.
   *
   * ```javascript
   * const grid = new Grid({
   *     columns : [
   *         {
   *             field : 'age',
   *             text : 'Age',
   *             sortable(lhs, rhs) {
   *               // Custom sorting, see Array#sort
   *             }
   *         }
   *     ],
   *
   *     features : {
   *         sort : {
   *             prioritizeColumns : true
   *         }
   *     }
   * });
   *
   * grid.store.sort('age');
   * ```
   *
   * @config {Boolean}
   * @default
   */
  prioritizeColumns: false,
  /**
   * Set to `false` to not show the sort icon when hovering a Column header.
   * @prp {Boolean}
   * @default
   */
  showIconOnColumnHover: true,
  /**
   * By default, clicking anywhere on the header text toggles the sorting state of a column.
   *
   * Configure this as `false` to only toggle the sorting state of a column on click of the
   * "arrow" icon within the grid header.
   * @config {Boolean}
   * @default false
   */
  toggleOnHeaderClick: true
});
Sort.featureClass = "b-sort";
Sort._$name = "Sort";
GridFeatureManager.registerFeature(Sort, true);

// ../Grid/lib/Grid/feature/StickyCells.js
var StickyCells = class extends InstancePlugin {
  static get defaultConfig() {
    return {
      /**
       * A CSS selector which must identify the content within your grid row which you
       * require to be pinned to the grid while the row if the topmost row, and remains visible.
       * @config {String}
       */
      contentSelector: null,
      currentTopRowCls: "b-sticky-cells-current-top-row"
    };
  }
  // Plugin configuration. This plugin chains some of the functions in Grid.
  static get pluginConfig() {
    return {
      before: ["renderRows"]
    };
  }
  //endregion
  //region Init
  construct(grid, config) {
    super.construct(grid, config);
    grid.ion({
      scroll: "onGridScroll",
      thisObj: this
    });
    Object.assign(this, DomHelper.createElement({
      reference: "element",
      parent: grid.element,
      className: "b-grid-sticky-row",
      children: [{
        reference: "contentElement",
        className: "b-grid-cell"
      }]
    }));
    this.removeClasses = {
      "b-focused": false,
      "b-hover": false,
      "b-selected": false,
      [this.currentTopRowCls]: false
    };
  }
  renderRows() {
    this.element.classList.add("b-hide-visibility");
  }
  onGridScroll() {
    const me = this, {
      client: grid,
      element,
      contentElement
    } = me, gridViewport = Rectangle.client(grid.bodyContainer).roundPx(), currentTopRow = grid.rowManager.getRowAt(gridViewport.y), topRowChanged = currentTopRow !== me.currentTopRow;
    if (currentTopRow) {
      if (topRowChanged) {
        if (me.currentTopRow) {
          me.currentTopRow.removeCls(me.currentTopRowCls);
          me.currentTopRow.removeCls("b-not-enough-height");
        }
        me.currentTopRow = currentTopRow;
        currentTopRow.addCls(me.currentTopRowCls);
        contentElement.innerHTML = "";
        contentElement.appendChild(me.updateStickyContent());
      }
      const notEnoughHeight = me.currentTopRow.bottom - me.client.scrollable.y <= me.stickyContentHeight;
      me.element.classList[notEnoughHeight ? "add" : "remove"]("b-hide-visibility");
      me.currentTopRow[notEnoughHeight ? "addCls" : "removeCls"]("b-not-enough-height");
      gridViewport.y += me.stickyContentTop;
      DomHelper.alignTo(element, me.stickyEls[0], { align: "t0-t0", constrainTo: gridViewport }, true);
    }
    me.lastProcessedTopRow = currentTopRow;
  }
  updateStickyContent() {
    const me = this, {
      currentTopRow,
      removeClasses
    } = me, rowClasses = {
      "b-grid-sticky-row": 1
    }, cellClasses = {}, stickyContent = me.stickyContent || (me.stickyContent = document.createDocumentFragment()), stickyEls = me.stickyEls || (me.stickyEls = []);
    stickyEls.forEach((e) => {
      e.classList.remove("b-sticky-content-el");
    });
    stickyEls.length = 0;
    currentTopRow.eachElement((rowEl) => {
      stickyEls.push(...rowEl.querySelectorAll(me.contentSelector));
    });
    while (stickyContent.firstChild) {
      stickyContent.remove(stickyContent.firstChild);
    }
    me.stickyContentHeight = 0;
    me.stickyContentTop = 0;
    stickyEls.map((e) => {
      e.closest(".b-grid-cell").classList.forEach((cls) => cellClasses[cls] = 1);
      e.closest(".b-grid-row").classList.forEach((cls) => rowClasses[cls] = 1);
      Object.assign(cellClasses, removeClasses);
      Object.assign(rowClasses, removeClasses);
      const eTop = e.offsetTop;
      me.stickyContentTop = Math.max(me.stickyContentTop, eTop);
      e.style.alignSelf = "flex-end";
      me.stickyContentHeight = Math.max(me.stickyContentHeight, me.currentTopRow.height - e.offsetTop + eTop);
      e.style.alignSelf = "";
      const result = e.cloneNode(true);
      e.classList.add("b-sticky-content-el");
      stickyContent.appendChild(result);
      return result;
    });
    cellClasses["b-focused"] = false;
    DomHelper.syncClassList(me.contentElement, cellClasses);
    DomHelper.syncClassList(me.element, rowClasses);
    return stickyContent;
  }
};
//region Config
__publicField(StickyCells, "$name", "StickyCells");
StickyCells._$name = "StickyCells";
GridFeatureManager.registerFeature(StickyCells, false);

// ../Grid/lib/Grid/feature/Stripe.js
var Stripe = class extends InstancePlugin {
  static get pluginConfig() {
    return {
      chain: [
        "beforeRenderRow"
      ]
    };
  }
  doDisable(disable) {
    if (!this.isConfiguring) {
      this.client.refreshRows();
    }
    super.doDisable(disable);
  }
  /**
   * Applies even/odd CSS when row is rendered
   * @param {Grid.row.Row} rowModel
   * @private
   */
  beforeRenderRow({ row, record }) {
    var _a5, _b2;
    const { enabled } = this, { isGroupHeader } = record, groupParent = this.client.store.isGrouped && record.instanceMeta(this.client.store).groupParent, even = ((_b2 = (_a5 = groupParent == null ? void 0 : groupParent.groupChildren) == null ? void 0 : _a5.indexOf(record)) != null ? _b2 : row.dataIndex) % 2 === 0;
    row.assignCls({
      "b-even": enabled && !isGroupHeader && even,
      "b-odd": enabled && !isGroupHeader && !even
    });
  }
};
__publicField(Stripe, "$name", "Stripe");
Stripe._$name = "Stripe";
GridFeatureManager.registerFeature(Stripe);

// ../Grid/lib/Grid/feature/Summary.js
var Summary = class extends SummaryFormatter_default(InstancePlugin) {
  //region Config
  static get configurable() {
    return {
      /**
       * Set to `true` to sum values of selected row records
       * @prp {Boolean}
       * @default false
       */
      selectedOnly: null,
      /**
       * Set to `true` to only refresh columns with fields matching the changed fields after a field update
       * @prp {Boolean}
       * @default false
       */
      refreshOnlyAffectedSummaries: null,
      hideFooters: false
    };
  }
  // Plugin configuration. This plugin chains some of the functions in Grid.
  static get pluginConfig() {
    return {
      chain: ["renderRows", "bindStore"]
    };
  }
  construct(grid, config) {
    this.grid = grid;
    super.construct(grid, config);
    this.bindStore(grid.store);
    grid.hideFooters = this.hideFooters;
  }
  bindStore(store) {
    this.detachListeners("store");
    store.ion({
      name: "store",
      change: "onStoreChange",
      thisObj: this
    });
    if (!this.isConfiguring) {
      this.refreshSummaries();
    }
  }
  get store() {
    return this.grid.store;
  }
  doDestroy() {
    super.doDestroy();
  }
  doDisable(disable) {
    super.doDisable(disable);
    const { client } = this;
    if (disable) {
      client.element.classList.add("b-summary-disabled");
    } else {
      this.refreshSummaries();
      client.element.classList.remove("b-summary-disabled");
      client.eachSubGrid((subGrid) => subGrid.scrollable.syncPartners());
    }
  }
  //endregion
  //region Render
  renderRows() {
    this.refreshSummaries();
  }
  /**
   * Updates summaries. Summaries are displayed as tables in footer (styling left out to keep brief):
   * ```
   * <table>
   *     <tr><td colspan="2">0</td></tr> // { sum : 'min' } Only a calculation, span entire table
   *     <tr><td>Max</td><td>10</td></tr> // { sum : 'max', label: 'Max' } Label + calculation
   *     <tr><td>Max</td><td>10</td></tr> // { sum : 'sum', label: 'Max' } Label + calculation
   * </table>
   * ```
   * @private
   */
  refreshSummaries(fields) {
    const me = this, { grid, store } = me, cells = fields ? null : DomHelper.children(grid.element, ".b-grid-footer"), selectedOnly = me.selectedOnly && grid.selectedRecords.length > 0, records = (store.isFiltered ? store.storage.values : store.allRecords).filter((r) => !r.isSpecialRow && (!selectedOnly || grid.isSelected(r))), columns = fields ? fields.map((field) => grid.columns.get(field)) : grid.columns.visibleColumns;
    columns.forEach((column, i) => {
      var _a5;
      (_a5 = column.summaries) == null ? void 0 : _a5.forEach((config) => {
        if ("seed" in config) {
          if (!("initialSeed" in config)) {
            config.initialSeed = config.seed;
          }
          if (["number", "string", "date"].includes(typeof config.initialSeed)) {
            config.seed = config.initialSeed;
          } else {
            config.seed = Object.assign({}, config.initialSeed);
          }
        }
      });
      const cellElement = fields ? grid.element.querySelector(`.b-grid-footer[data-column-id=${column.id}]`) : cells[i];
      if (!(cellElement == null ? void 0 : cellElement.dataset.column)) {
        return;
      }
      const html = me.generateHtml(column, records, "b-grid-footer-summary");
      if (column.summaries ? column.summaries.length : column.sum ? 1 : 0) {
        if (!cellElement.children.length) {
          cellElement.innerHTML = html;
        } else {
          DomHelper.sync(html, cellElement.firstElementChild);
        }
      }
    });
  }
  //endregion
  //region Events
  /**
   * Updates summaries on store changes (except record update, handled below)
   * @private
   */
  onStoreChange({ action, changes }) {
    let shouldUpdate = true;
    if (this.disabled) {
      return;
    }
    if (action === "update") {
      shouldUpdate = Object.keys(changes).some((field) => {
        const colField = this.grid.columns.get(field);
        return Boolean(colField) && (Boolean(colField.sum) || Boolean(colField.summaries));
      });
    }
    if (shouldUpdate) {
      this.refreshSummaries(action === "update" && this.refreshOnlyAffectedSummaries ? Object.keys(changes) : void 0);
    }
  }
  //endregion
  updateSelectedOnly(value2) {
    const me = this;
    me.detachListeners("selectionChange");
    if (value2) {
      me.grid.ion({
        name: "selectionChange",
        selectionChange: me.refresh,
        thisObj: me
      });
    }
    me.refresh();
  }
  /**
   * Refreshes the summaries
   */
  refresh() {
    this.refreshSummaries();
  }
};
//endregion
//region Init
__publicField(Summary, "$name", "Summary");
Summary.featureClass = "b-summary";
Summary._$name = "Summary";
GridFeatureManager.registerFeature(Summary);

// ../Grid/lib/Grid/feature/Tree.js
var immediatePromise6 = Promise.resolve();
var Tree = class extends InstancePlugin.mixin(Delayable_default) {
  // Plugin configuration. This plugin chains some functions in Grid.
  static get pluginConfig() {
    return {
      assign: ["collapseAll", "expandAll", "collapse", "expand", "expandTo", "toggleCollapse"],
      chain: ["onElementClick", "bindStore", "beforeRenderRow"]
    };
  }
  //endregion
  //region Init
  construct(client, config) {
    var _a5;
    super.construct(client, config);
    if (!this.treeColumn && ((_a5 = client.initialConfig.columns) == null ? void 0 : _a5.length) > 0) {
      console.info("To use the tree feature, one column should be configured with `type: 'tree'`");
    }
    client.store && this.bindStore(client.store);
  }
  doDisable(disable) {
    if (disable) {
      throw new Error("Tree feature cannot be disabled");
    }
  }
  get store() {
    return this.client.store;
  }
  get treeColumn() {
    const me = this, { columns } = me.client;
    if (!me._treeColumn || !columns.includes(me._treeColumn)) {
      me._treeColumn = columns.find((column) => column.isTreeColumn);
    }
    return me._treeColumn;
  }
  bindStore(store) {
    this.detachListeners("store");
    store.ion({
      name: "store",
      beforeLoadChildren: "onBeforeLoadChildren",
      loadChildren: "onLoadChildren",
      loadChildrenException: "onLoadChildrenException",
      beforeToggleNode: "onBeforeToggleNode",
      beforeLazyLoad: "onBeforeLoadChildren",
      afterLazyLoad: "onLoadChildren",
      thisObj: this
    });
    if (store.lazyLoad && !store.tree) {
      console.warn("To use the Tree feature on a lazy loaded store, requires the Store to be configured with `tree: true`");
    }
  }
  //endregion
  //region Expand & collapse
  /**
   * Collapse an expanded node or expand a collapsed. Optionally forcing a certain state.
   * This function is exposed on Grid and can thus be called as `grid.toggleCollapse()`
   * @param {String|Number|Core.data.Model} idOrRecord Record (the node itself) or id of a node to toggle
   * @param {Boolean} [collapse] Force collapse (true) or expand (false)
   * @on-owner
   * @category Tree
   */
  async toggleCollapse(idOrRecord, collapse) {
    if (idOrRecord == null) {
      throw new Error("Tree#toggleCollapse must be passed a record");
    }
    const me = this, { store, client } = me, { rowManager } = client, record = store.getById(idOrRecord), meta = record.instanceMeta(store);
    let row = rowManager.getRowFor(record);
    record.generation++;
    if (await store.toggleCollapse(record, collapse)) {
      if (me.isDestroyed) {
        return;
      }
      row = rowManager.getRowFor(record);
      if (row && record.ancestorsExpanded()) {
        row.render(null, null, false);
      }
      if (!me.isTogglingNode) {
        client.element.classList.add("b-toggling-node");
        me.isTogglingNode = true;
        me.requestAnimationFrame(() => {
          client.element.classList.remove("b-toggling-node");
          me.isTogglingNode = false;
        });
      }
      client.trigger(meta.collapsed ? "collapseNode" : "expandNode", { record });
      client.trigger("toggleNode", { record, collapse: meta.collapsed });
    }
  }
  /**
   * Collapse a single node.
   * This function is exposed on Grid and can thus be called as `grid.collapse()`
   * @param {String|Number|Core.data.Model} idOrRecord Record (the node itself) or id of a node to collapse
   * @on-owner
   * @category Tree
   */
  async collapse(idOrRecord) {
    return this.toggleCollapse(idOrRecord, true);
  }
  /**
   * Expand a single node.
   * This function is exposed on Grid and can thus be called as `grid.expand(idOrRecord)`
   * @param {String|Number|Core.data.Model} idOrRecord Record (the node itself) or id of a node to expand
   * @on-owner
   * @category Tree
   */
  async expand(idOrRecord) {
    return this.toggleCollapse(idOrRecord, false);
  }
  onBeforeToggleNode({ record, collapse }) {
    this.client.trigger("beforeToggleNode", { record, collapse });
  }
  onBeforeLoadChildren({ source: store, params, parent = params && store.getById(params[store.modelClass.idField]) }) {
    var _a5;
    (_a5 = this.client.rowManager.getRowFor(parent)) == null ? void 0 : _a5.addCls("b-loading-children");
  }
  onLoadChildren({ source: store, params, parent = params && store.getById(params[store.modelClass.idField]) }) {
    var _a5;
    (_a5 = this.client.rowManager.getRowFor(parent)) == null ? void 0 : _a5.removeCls("b-loading-children");
  }
  onLoadChildrenException({ record }) {
    const row = this.client.rowManager.getRowFor(record);
    row == null ? void 0 : row.removeCls("b-loading-children");
  }
  /**
   * Expand or collapse all nodes, as specified by param, starting at the passed node (which defaults to the root node)
   * @param {Boolean} [collapse] Set to `true` to collapse, `false` to expand (defaults to `true`)
   * @param {Core.data.Model} [topNode] The topmost node from which to cascade a collapse.
   * Defaults to the {@link Core.data.Store#property-rootNode}. Not included in the cascade if
   * the root node is being used.
   * @category Tree
   */
  async expandOrCollapseAll(collapse = true, topNode = this.store.rootNode) {
    const { client, store } = this, { animateTreeNodeToggle } = client, promises = [], childRecords = [];
    client.trigger("beforeToggleAllNodes", { collapse });
    client.animateTreeNodeToggle = false;
    store.suspendEvents();
    store.traverse((record) => {
      const gridMeta = record.instanceMeta(store);
      if (!record.isLeaf) {
        if (collapse && !gridMeta.collapsed) {
          this.toggleCollapse(record, true);
          childRecords.push(...record.children);
        } else if (!collapse && gridMeta.collapsed) {
          if (Array.isArray(record.children)) {
            childRecords.push(...record.children);
          }
          promises.push(this.toggleCollapse(record, false));
        }
      }
    }, topNode, topNode === store.rootNode);
    store.resumeEvents();
    return (collapse ? immediatePromise6 : Promise.all(promises)).then(() => {
      if (childRecords.length) {
        if (collapse) {
          store.trigger("remove", { records: childRecords, isCollapse: true, isCollapseAll: true });
        } else {
          store.trigger("add", { records: childRecords, isExpand: true, isExpandAll: true });
        }
      }
      client.refreshRows(null, collapse);
      client.trigger("toggleAllNodes", { collapse });
      client.animateTreeNodeToggle = animateTreeNodeToggle;
    });
  }
  /**
   * Collapse all nodes.
   * This function is exposed on Grid and can thus be called as `grid.collapseAll()`
   * @on-owner
   * @category Tree
   */
  async collapseAll() {
    return this.expandOrCollapseAll(true);
  }
  /**
   * Expand all nodes.
   * This function is exposed on Grid and can thus be called as `grid.expandAll()`
   * @on-owner
   * @category Tree
   */
  async expandAll() {
    return this.expandOrCollapseAll(false);
  }
  /**
   * Expands parent nodes to make this node "visible".
   * This function is exposed on Grid and can thus be called as `grid.expandTo()`
   * @param {String|Number|Core.data.Model|String[]|Number[]|Core.data.Model[]} idOrRecord Record (the node itself),
   * or id of a node. Also accepts arrays of the same types.
   * @param {Boolean} [scrollIntoView=true] A flag letting you control whether to scroll the record into view
   * @on-owner
   * @async
   * @category Tree
   */
  async expandTo(idOrRecord, scrollIntoView = true) {
    var _a5, _b2, _c2, _d;
    const me = this, { store, client } = me, { animateTreeNodeToggle } = client;
    if (Array.isArray(idOrRecord)) {
      if (idOrRecord.length > 0) {
        client.suspendRefresh();
        for (let i = idOrRecord.length - 1; i >= 0; i--) {
          const record2 = store.getById(idOrRecord[i]);
          if (i === 0) {
            (_a5 = client.resumeRefresh) == null ? void 0 : _a5.call(client, false);
            client.rowManager.refresh();
          }
          await ((_b2 = me.expandTo) == null ? void 0 : _b2.call(me, record2, i === 0));
        }
      }
      return;
    }
    const record = store.getById(idOrRecord);
    if (record.instanceMeta(me.store).hiddenByCollapse === false) {
      return;
    }
    client.animateTreeNodeToggle = false;
    if (!record.ancestorsExpanded()) {
      const parents = [];
      for (let parent = record.parent; parent && !parent.isRoot; parent = parent.parent) {
        if (!parent.isExpanded(store)) {
          parents.unshift(parent);
        }
      }
      client.suspendRefresh();
      for (const parent of parents) {
        if (!me.isDestroyed) {
          await me.toggleCollapse(parent, false);
        }
      }
      (_c2 = client.resumeRefresh) == null ? void 0 : _c2.call(client, false);
      (_d = client.refreshRows) == null ? void 0 : _d.call(client);
    }
    client.animateTreeNodeToggle = animateTreeNodeToggle;
    if (!me.isDestroyed && scrollIntoView) {
      await client.scrollRowIntoView(record);
    }
  }
  /**
   * Expands the parent nodes in the tree to the provided depth.
   * @param {Number} level The depth to expand to. Passing level `0` means the top level root parents will be expanded.
   * @param {Boolean} [collapseAll=false] Pass `true` to collapse all nodes before expanding
   * @category Tree
   */
  async expandToLevel(level, collapseAll = false) {
    const { store, client } = this, { animateTreeNodeToggle } = client, expandChildren = (parent) => {
      parent.children.forEach((record) => {
        if (!record.isLeaf) {
          if (record.instanceMeta(store).collapsed) {
            this.toggleCollapse(record, false);
          }
          if (record.childLevel < level) {
            expandChildren(record);
          }
        }
      });
    };
    store.suspendEvents();
    client.suspendRefresh();
    if (collapseAll) {
      await this.collapseAll();
    }
    if (!client.isDestroyed) {
      client.animateTreeNodeToggle = false;
      expandChildren(store.rootNode);
      store.resumeEvents();
      client.resumeRefresh(true);
      client.animateTreeNodeToggle = animateTreeNodeToggle;
    }
  }
  //endregion
  //region Events
  /**
   * Called when user clicks somewhere in the grid. Expand/collapse node on icon click.
   * @private
   */
  onElementClick(event) {
    const me = this, target = event.target, cellData = me.client.getCellDataFromEvent(event), clickedExpander = target.closest(".b-tree-expander");
    if (clickedExpander) {
      event.preventDefault();
    }
    if (clickedExpander || me.expandOnCellClick && (cellData == null ? void 0 : cellData.record.isParent)) {
      me.toggleCollapse(cellData.record);
    }
  }
  /**
   * Called on key down in grid. Expand/collapse node on [space]
   * @private
   */
  toggleCollapseByKey() {
    var _a5;
    const { focusedCell } = this.client;
    if ((focusedCell == null ? void 0 : focusedCell.rowIndex) > -1 && !((_a5 = this.client.features.cellEdit) == null ? void 0 : _a5.isEditing) && focusedCell.record.isParent && document.activeElement.closest(".b-grid-cell")) {
      this.toggleCollapse(focusedCell.id);
      return true;
    }
    return false;
  }
  //endregion
  //region Rendering
  beforeRenderRow({ record, cls }) {
    if (!record.isLeaf) {
      const { store } = this.client, isCollapsed = !record.isExpanded(store);
      cls["b-tree-parent-row"] = 1;
      cls["b-tree-collapsed"] = isCollapsed;
      cls["b-tree-expanded"] = !isCollapsed;
      cls["b-loading-children"] = record.instanceMeta(store).isLoadingChildren;
    } else {
      cls["b-tree-parent-row"] = 0;
      cls["b-tree-collapsed"] = 0;
      cls["b-tree-expanded"] = 0;
      cls["b-loading-children"] = 0;
    }
  }
  //endregion
  // Expands tree if single column.
  // Called by default on ArrowRight
  expandIfSingleColumn() {
    if (this.client.columns.count === 1) {
      return this.expandByKey();
    }
    return false;
  }
  // Expands tree on Shift+ArrowRight by default.
  expandByKey() {
    const me = this, { client } = me, { focusedCell } = client, record = focusedCell == null ? void 0 : focusedCell.record;
    if (record && (focusedCell == null ? void 0 : focusedCell.column.tree) && record.isParent && record.instanceMeta(client.store).collapsed) {
      me.expand(record);
      return true;
    }
    return false;
  }
  collapseIfSingleColumn() {
    if (this.client.columns.count === 1) {
      return this.collapseByKey();
    }
    return false;
  }
  collapseByKey() {
    const me = this, { client } = me, { focusedCell } = client, record = focusedCell == null ? void 0 : focusedCell.record;
    if ((focusedCell == null ? void 0 : focusedCell.column.tree) && record) {
      if (record.isParent && !record.instanceMeta(client.store).collapsed) {
        me.collapse(record);
        return true;
      }
      if (record.parent && !record.parent.isRoot) {
        client.deselectAll();
        client.focusCell({
          record: record.parent,
          column: focusedCell.column
        });
        return true;
      }
    }
    return false;
  }
  updateTreeLines() {
    if (!this.isConfiguring) {
      this.client.refreshRows();
    }
  }
};
//region Config
__publicField(Tree, "$name", "Tree");
__publicField(Tree, "configurable", {
  /**
   * Expand parent nodes when clicking on their cell
   * @prp {Boolean}
   * @default
   */
  expandOnCellClick: false,
  /**
   * See {@link #keyboard-shortcuts Keyboard shortcuts} for details
   * @config {Object<String,KeyMapConfig>}
   */
  keyMap: {
    " ": "toggleCollapseByKey",
    ArrowRight: "expandIfSingleColumn",
    "Shift+ArrowRight": "expandByKey",
    ArrowLeft: "collapseIfSingleColumn",
    "Shift+ArrowLeft": "collapseByKey"
  },
  /**
   * Show or hide tree lines
   * @prp {Boolean}
   * @default
   */
  treeLines: false
});
Tree.featureClass = "b-tree";
Tree._$name = "Tree";
GridFeatureManager.registerFeature(Tree, false, "Grid");
GridFeatureManager.registerFeature(Tree, true, "TreeGrid");

// ../Grid/lib/Grid/feature/TreeGroup.js
var TreeGroup = class extends SummaryFormatter_default(InstancePlugin) {
  static get pluginConfig() {
    return {
      chain: ["populateHeaderMenu"],
      assign: ["group", "clearGroups", "refreshGroups"]
    };
  }
  construct(grid, config) {
    this.treeColumn = grid.columns.find((col) => col.isTreeColumn);
    super.construct(grid, config);
    this._levels = this._levels || [];
    if (!grid.hasFeature("tree")) {
      throw new Error("The TreeGroup feature requires the Tree feature to be enabled");
    }
    grid.rowManager.ion({
      renderCell: "renderCell",
      prio: 1e3,
      thisObj: this
    });
  }
  applyPluginConfig() {
    this.scheduleRefreshGroups = FunctionHelper.createBuffered(this.refreshGroups, this.refreshGroupsTimeout, this);
    return super.applyPluginConfig(...arguments);
  }
  processParentData(parentData) {
    const me = this;
    if (me.parentCls) {
      parentData.cls = me.parentCls;
    }
  }
  processTransformedData(transformedData) {
  }
  async waitForLoad() {
    var _a5;
    const me = this;
    if (me.originalStore.isLoading) {
      await me.originalStore.await("load", false);
      if (me.isDestroyed) {
        return;
      }
    }
    const { crudManager } = this.client;
    if (crudManager) {
      if (crudManager.isLoadingOrSyncing || crudManager._autoLoadPromise) {
        await crudManager.await("requestDone");
      }
      if (me.isDestroyed) {
        return;
      }
      await ((_a5 = me.client.project) == null ? void 0 : _a5.commitAsync());
    }
  }
  async waitForReadiness() {
    await this.waitForLoad();
    if (this.scheduleRefreshGroups.isPending) {
      await this.client.await("treeGroupChange", false);
    }
  }
  updateOriginalStore(originalStore) {
    const me = this;
    me.store = me.initialConfig.store;
    me.detachListeners("originalStore");
    originalStore == null ? void 0 : originalStore.ion({
      name: "originalStore",
      refresh: me.onOriginalStoreRefresh,
      add: me.onOriginalStoreChanged,
      update: me.onOriginalStoreUpdate,
      remove: me.onOriginalStoreChanged,
      removeAll: me.onOriginalStoreChanged,
      thisObj: me
    });
  }
  changeStore(store) {
    var _a5;
    const { originalStore } = this;
    if (originalStore) {
      store = new originalStore.constructor({
        reapplyFilterOnAdd: true,
        reapplyFilterOnUpdate: true,
        tree: true,
        modelClass: originalStore.modelClass,
        load: (_a5 = originalStore.load) == null ? void 0 : _a5.bind(originalStore),
        commit: originalStore.commit.bind(originalStore),
        remove: (records, silent) => {
          records = ArrayHelper.asArray(records);
          const toRemoveFromOriginal = [], toRemove = [];
          if (store.isApplyingLevels) {
            toRemove.push(...records);
          } else {
            for (const rec of records) {
              if (rec.isLinked) {
                toRemoveFromOriginal.push(rec.$original);
              } else {
                toRemove.push(rec);
              }
            }
          }
          if (toRemove.length) {
            originalStore.remove.call(store, toRemove, silent);
          }
          if (toRemoveFromOriginal.length) {
            originalStore.remove(toRemoveFromOriginal, silent);
          }
        },
        removeAll: originalStore.removeAll.bind(originalStore)
      }, store);
    }
    return store;
  }
  async applyLevels(levels) {
    const me = this, { client, treeColumn, expandParents } = me;
    let { store } = client, result = null, treeColumnField;
    const { modelClass } = store;
    if ((treeColumn == null ? void 0 : treeColumn.field) && modelClass.getFieldDefinition(treeColumn.field)) {
      treeColumnField = modelClass.getFieldDataSource(treeColumn.field);
    }
    levels = levels || [];
    if (levels.length === 0 && me.isConfiguring) {
      return;
    }
    me._levels = levels;
    me.isApplying++;
    if (!me.originalStore) {
      me.originalStore = store;
      client.suspendRefresh();
      client.store = store = me.store;
      client.resumeRefresh(false);
    }
    await me.waitForLoad();
    if (me.isDestroyed) {
      return;
    }
    client.suspendRefresh();
    if (levels.length > 0) {
      const existingLinksOf = /* @__PURE__ */ new Map();
      for (const record of store.getAllDataRecords(false)) {
        if (record.isLeaf && record.isLinked) {
          existingLinksOf.set(record.$original, record);
        }
      }
      const leafLinks = me.originalStore.getAllDataRecords(false).flatMap((record) => {
        if (record.isLeaf) {
          return existingLinksOf.has(record) ? existingLinksOf.get(record) : record.link();
        }
        return [];
      });
      for (const record of leafLinks) {
        const meta = record.instanceMeta(store.id);
        meta.collapsed = false;
        meta.hidden = false;
      }
      result = store.treeifyData(leafLinks, levels, (parentData) => {
        if (treeColumnField) {
          ObjectHelper.setPath(parentData, treeColumnField, parentData.key);
        }
        const existingRecord = store.getById(parentData.id);
        if (expandParents && !(existingRecord && store.syncDataOnLoad)) {
          parentData.expanded = true;
        }
        me.processParentData(parentData);
      }, false, (value2, fieldName) => {
        const column = fieldName && client.columns.get(fieldName);
        if (column == null ? void 0 : column.formatValue) {
          value2 = column.formatValue(value2);
        } else if (column == null ? void 0 : column.isCheckColumn) {
          value2 = Boolean(value2);
        }
        return value2;
      });
      me.processTransformedData(result);
      await me.trigger("beforeDataLoad", { store, data: result.children });
      store.isApplyingLevels = true;
      store.data = result.children;
      store.isApplyingLevels = false;
    } else {
      client.store = me.originalStore;
      me.detachListeners("originalStore");
      me.originalStore = null;
    }
    me.isApplying--;
    client.element.classList.toggle("b-grid-treegrouped", levels.length > 0);
    client.resumeRefresh(false);
    me.refresh();
    client.trigger("treeGroupChange", { levels });
  }
  refresh() {
    if (this.client.isPainted) {
      this.client.renderRows(false);
    }
  }
  doDisable(disable) {
    if (disable) {
      this.clearGroups();
    }
    super.doDisable(disable);
  }
  onOriginalStoreUpdate({ records, changes }) {
    if (this.levels.some((level) => (level.fieldName || level) in changes)) {
      this.scheduleRefreshGroups();
    } else {
      Object.keys(changes).forEach((field) => {
        const column = this.client.columns.get(field);
        if ((column == null ? void 0 : column.isVisible) && column.hasSummary) {
          column.refreshCells();
        }
      });
    }
  }
  onOriginalStoreChanged() {
    this.scheduleRefreshGroups();
  }
  onOriginalStoreRefresh({ action }) {
    if (action === "dataset" || action === "filter") {
      this.scheduleRefreshGroups();
    }
  }
  updateLevels(levels, old) {
    const me = this, { client } = me;
    if (me.hideGroupedColumns) {
      old == null ? void 0 : old.forEach((field) => {
        field = field.fieldName || field;
        if (!levels.some((level) => (level.fieldName || level) === field)) {
          client.columns.get(field).show();
        }
      });
      levels == null ? void 0 : levels.forEach((field) => {
        field = field.fieldName || field;
        if (!old || !old.some((fn2) => fn2.fieldName === field)) {
          client._suspendRenderContentsOnColumnsChanged = true;
          client.columns.get(field).hide();
          client._suspendRenderContentsOnColumnsChanged = false;
        }
      });
    }
    if (levels || !me.isConfiguring) {
      me.updatePromise = me.applyLevels(levels);
      client.renderContents();
    }
  }
  get groupFields() {
    return this.levels.map((level) => level.fieldName || level);
  }
  /**
   * Transforms the data according to the supplied levels.
   *
   * Yields the same result as assigning to {@link #property-levels}.
   *
   * ```javascript
   * // Transform into a tree with two parent levels
   * grid.group('status', record => (record.percentDone % 10) * 10);
   * ```
   *
   * @param {Array<String|Grid.column.Column|Function(Core.data.Model) : any>} levels Field names or functions use to generate parents in resulting tree.
   * @on-owner
   * @non-lazy-load
   * @category Tree grouping
   */
  async group(levels) {
    ObjectHelper.assertArray(levels, "group()");
    await this.applyLevels(levels);
  }
  /**
   * Clears the previously applied transformation, restoring data to its initial state.
   *
   * Yields the same result as assigning `null` to {@link #property-levels}.
   *
   * ```javascript
   * // Restore original data
   * grid.clearGroups();
   * ```
   * @on-owner
   * @non-lazy-load
   * @category Tree grouping
   */
  async clearGroups() {
    if (this.isGrouped) {
      this.levels = [];
      await this.updatePromise;
    }
  }
  /**
   * Refreshes the store tree grouping by re-applying the current transformation.
   *
   * ```javascript
   * // Refresh groups
   * grid.refreshGroups();
   * ```
   * @on-owner
   * @non-lazy-load
   * @category Tree grouping
   * @private
   */
  refreshGroups() {
    var _a5;
    return (_a5 = this.applyLevels) == null ? void 0 : _a5.call(this, this._levels);
  }
  /**
   * Indicates if the feature has applied grouping and the component uses
   * a transformed version of the store.
   * @property {Boolean}
   */
  get isGrouped() {
    return !this.disabled && this._levels.length > 0;
  }
  /**
   * Supply items for headers context menu.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Object<String,MenuItemConfig|Boolean|null>} options.items A named object to describe menu items
   * @internal
   */
  populateHeaderMenu({ column, items: items2 }) {
    const me = this, groupable = column.groupable !== false && !column.isTreeColumn;
    let separator = false;
    if (groupable && !me.isGroupedByField(column.field)) {
      items2.groupAsc = {
        text: "L{group}",
        localeClass: me,
        icon: "b-fw-icon b-icon-group-asc",
        separator: true,
        weight: 400,
        disabled: me.disabled,
        onItem: () => me.addGrouper(column)
      };
      separator = true;
    }
    if (me.isGrouped) {
      if (me.isGroupedByField(column.field)) {
        items2.groupRemove = {
          text: "L{stopGroupingThisColumn}",
          localeClass: me,
          icon: "b-fw-icon b-icon-clear",
          separator: !separator,
          weight: 420,
          disabled: me.disabled,
          onItem: () => me.removeGrouper(column)
        };
        separator = true;
      }
      items2.groupRemoveAll = {
        text: "L{stopGrouping}",
        localeClass: me,
        icon: "b-fw-icon b-icon-clear",
        separator: !separator,
        weight: 420,
        disabled: me.disabled,
        onItem: () => me.clearGroups()
      };
    }
  }
  addGrouper(column) {
    this.levels = this.levels.concat(column.field);
  }
  isGroupedByField(field) {
    return this.levels.find((groupFn) => groupFn.fieldName === field);
  }
  removeGrouper(column) {
    this.levels.splice(this.levels.findIndex((groupFn) => groupFn.fieldName === column.field), 1);
    this.levels = this.levels.slice();
  }
  renderCell({ column, cellElement, row, record, size, isFirstColumn }) {
    if (this.isGrouped && !this.disabled && record.generatedParent && column.hasSummary) {
      const html = this.generateHtml(column, record.allChildren, "b-grid-treegroup-summary");
      if (!cellElement.children.length) {
        cellElement.innerHTML = html;
      } else {
        DomHelper.sync(html, cellElement.firstElementChild);
      }
    }
  }
};
__publicField(TreeGroup, "$name", "TreeGroup");
__publicField(TreeGroup, "configurable", {
  /**
   * An array of model field names or functions used to determine the levels in the resulting tree.
   *
   * When supplying a function, it will be called for each leaf in the original data, and it is expected to return
   * an atomic value used to determine which parent the leaf will be added to at that level.
   *
   * ```javascript
   * const grid = new TreeGrid({
   *     features : {
   *         treeGroup : {
   *             levels : [
   *                 // First level is determined by the value of the status field
   *                 'status',
   *                 // Second level by the result of this function
   *                 // (which puts percentdone 0-9 in one group, 10-19 into another and so on)
   *                 record => (record.percentDone % 10) * 10
   *             ]
   *         }
   *     }
   * });
   * ```
   *
   * The function form can also be used as a formatter/renderer of sorts, simply by returning a string:
   *
   * ```javascript
   * const grid = new TreeGrid({
   *     features : {
   *         treeGroup : {
   *             levels : [
   *                 record => `Status: ${record.status}`
   *             ]
   *         }
   *     }
   * });
   * ```
   *
   * Assigning `null` restores the tree structure to its original state.
   *
   * @prp {Array<String|Function(Core.data.Model) : any>} levels
   */
  levels: [],
  /**
   * CSS class to apply to the generated parents.
   *
   * @config {String}
   * @default
   */
  parentCls: "b-generated-parent",
  /**
   * A function letting you format the text shown in the generated parent group levels. This method will be provided
   * with the value produced by the column representing the grouped level. Each column's renderer method will be
   * provided an extra `isTreeGroup` param to indicate that the value will be used for a generated parent. `cellElement`
   * and other DOM specific args will be in the context of the tree column.
   *
   * ```javascript
   * const grid = new Grid({
   *     features : {
   *         treeGroup : {
   *             hideGroupedColumns : true,
   *             levels             : [
   *                 'priority'
   *             ],
   *             parentRenderer({ field, value, column, record }) {
   *                 // For generated group parent, prefix with the grouped column text
   *                 return column.text + ': ' + value;
   *             }
   *         }
   *     }
   * })
   * ```
   *
   * @config {Function}
   * @param {Object} data The rendering data representing the generated tree parent record
   * @param {String} data.field The field representing this group level (e.g. 'priority')
   * @param {*} data.value The value representing this group level (e.g. 'high')
   * @param {HTMLElement} data.cellElement Cell element, for adding CSS classes, styling etc. Can be `null`
   * in case of export.
   * @param {Grid.column.Column} data.column The value representing this group level (e.g. 'high')
   * @param {Grid.view.GridBase} data.grid This grid
   * @param {Core.data.Model} data.record The first record for this parent
   * @param {Boolean} data.isExport `true` if the record is being exported to Excel or a textual format, enabling special handling during export.
   * @param {Grid.row.Row} data.row The parent Row object. Can be `null` in case of export. Use the
   *   {@link Grid.row.Row#function-assignCls row's API} to manipulate CSS class names.
   * @returns {String}
   */
  parentRenderer: null,
  /**
   * True to hide grouped columns. Only supported when using String to define levels.
   *
   * @config {Boolean}
   */
  hideGroupedColumns: null,
  /**
   * The number of milliseconds to wait after {@link #function-scheduleRefreshGroups} call
   * before actually refreshing groups.
   * Each further {@link #function-scheduleRefreshGroups} call during that timeout will restart the timer.
   * @config {Number}
   * @default
   * @private
   */
  refreshGroupsTimeout: 100,
  /**
   * Specify as `true` to make generated parents start expanded.
   * @config {Boolean}
   * @default
   */
  expandParents: true,
  store: {},
  /**
   * The original store used by the component before applying grouping. Use this to modify / load data
   * while tree grouping is active.
   * @property {Core.data.Store}
   * @readonly
   */
  originalStore: null
});
__publicField(TreeGroup, "properties", {
  isApplying: 0
});
TreeGroup._$name = "TreeGroup";
GridFeatureManager.registerFeature(TreeGroup);

// ../Grid/lib/Grid/feature/export/Utils.js
var RowsRange = {
  all: "all",
  visible: "visible"
};
var PaperFormat = {
  A6: {
    width: 4.11,
    height: 5.81
  },
  A5: {
    width: 5.81,
    height: 8.25
  },
  A4: {
    width: 8.25,
    height: 11.69,
    portraitWidth: 8.3,
    portraitHeight: 11.7,
    landscapeWidth: 11.7,
    landscapeHeight: 8.26
  },
  A3: {
    width: 11.69,
    height: 16.49,
    portraitWidth: 11.7,
    // 16.54 works perfect in print, but in export (puppeteer) it results in extra empty page
    portraitHeight: 16.49,
    landscapeWidth: 16.54,
    landscapeHeight: 11.68
  },
  A2: {
    width: 16.54,
    height: 23.39,
    portraitWidth: 16.54,
    portraitHeight: 23.39,
    landscapeWidth: 23.39,
    landscapeHeight: 16.51
  },
  A1: {
    width: 23.39,
    height: 33.11,
    portraitWidth: 23.39,
    portraitHeight: 33.08,
    landscapeWidth: 33.11,
    landscapeHeight: 23.39
  },
  A0: {
    width: 33.11,
    height: 46.41,
    portraitWidth: 33.31,
    portraitHeight: 46.41,
    landscapeWidth: 46.81,
    landscapeHeight: 32.5
  },
  Legal: {
    width: 8.5,
    height: 14,
    portraitWidth: 8.5,
    portraitHeight: 14,
    landscapeWidth: 14,
    landscapeHeight: 8.15
  },
  Letter: {
    width: 8.5,
    height: 11,
    portraitWidth: 8.5,
    portraitHeight: 10.95,
    landscapeWidth: 11,
    landscapeHeight: 8.3
  }
};
function getPrintPaperSizeAdjustments() {
  if (BrowserHelper.isFirefox) {
    return {
      portraitWidth: 0,
      portraitHeight: 0,
      landscapeWidth: 0,
      landscapeHeight: 0.26
    };
  }
  if (BrowserHelper.isSafari) {
    return {
      portraitWidth: 0,
      portraitHeight: 0.1,
      landscapeWidth: 0,
      landscapeHeight: 0.5
    };
  }
  return {
    portraitWidth: 0,
    portraitHeight: 0,
    landscapeWidth: 0,
    landscapeHeight: 0.25
  };
}
var Orientation = {
  portrait: "portrait",
  landscape: "landscape"
};
var FileFormat = {
  pdf: "pdf",
  png: "png"
};
var FileMIMEType = {
  pdf: "application/pdf",
  png: "image/png"
};
var SupportedPaperSizes = [
  "a5",
  "a4",
  "a3",
  "b5",
  "b4",
  "jis-b5",
  "jis-b4",
  "letter",
  "legal",
  "ledger"
];
var isPaperSizeSupported = (size) => SupportedPaperSizes.includes(size.toLowerCase());

// ../Grid/lib/Grid/view/export/field/ExportRowsCombo.js
var ExportRowsCombo = class extends Combo {
  static get defaultConfig() {
    return {
      editable: false
    };
  }
  //endregion
  buildItems() {
    const me = this;
    return [
      { id: RowsRange.all, text: me.L("L{all}") },
      { id: RowsRange.visible, text: me.L("L{visible}") }
    ];
  }
};
//region Config
__publicField(ExportRowsCombo, "$name", "ExportRowsCombo");
// Factoryable type name
__publicField(ExportRowsCombo, "type", "exportrowscombo");
ExportRowsCombo.initClass();
ExportRowsCombo._$name = "ExportRowsCombo";

// ../Grid/lib/Grid/view/export/field/ExportOrientationCombo.js
var ExportOrientationCombo = class extends Combo {
  static get defaultConfig() {
    return {
      editable: false
    };
  }
  //endregion
  buildItems() {
    const me = this;
    return [
      { id: Orientation.portrait, text: me.L("L{portrait}") },
      { id: Orientation.landscape, text: me.L("L{landscape}") }
    ];
  }
};
//region Config
__publicField(ExportOrientationCombo, "$name", "ExportOrientationCombo");
// Factoryable type name
__publicField(ExportOrientationCombo, "type", "exportorientationcombo");
ExportOrientationCombo.initClass();
ExportOrientationCombo._$name = "ExportOrientationCombo";

// ../Grid/lib/Grid/view/export/ExportDialog.js
function buildComboItems(obj, fn2 = (x) => x) {
  return Object.keys(obj).map((key) => ({ id: key, text: fn2(key) }));
}
var ExportDialog = class extends Popup {
  static get configurable() {
    return {
      autoShow: false,
      autoClose: false,
      closable: true,
      centered: true,
      /**
       * Returns map of values of dialog fields.
       * @member {Object<String,Object>} values
       * @readonly
       */
      /**
       * Grid instance to build export dialog for
       * @config {Grid.view.Grid}
       */
      client: null,
      /**
       * Set to `false` to not preselect all visible columns when the dialog is shown
       * @config {Boolean}
       */
      autoSelectVisibleColumns: true,
      /**
       * Set to `false` to allow using PNG + Multipage config in export dialog
       * @config {Boolean}
       */
      hidePNGMultipageOption: true,
      /**
       * When set to `true` labels in the dialog will say `Print` instead of `Export`
       * @prp {Boolean}
       */
      useBrowserPrint: false,
      title: "L{exportSettings}",
      maxHeight: "80%",
      scrollable: {
        overflowY: true
      },
      defaults: {
        localeClass: this
      },
      items: {
        columnsField: {
          type: "combo",
          label: "L{ExportDialog.columns}",
          name: "columns",
          store: {},
          valueField: "id",
          displayField: "text",
          multiSelect: true,
          weight: 100,
          maxHeight: 100
        },
        rowsRangeField: {
          type: "exportrowscombo",
          label: "L{ExportDialog.rows}",
          name: "rowsRange",
          value: "all",
          weight: 200
        },
        exporterTypeField: {
          type: "combo",
          label: "L{ExportDialog.exporterType}",
          name: "exporterType",
          editable: false,
          value: "singlepage",
          displayField: "text",
          buildItems() {
            const dialog = this.parent;
            return dialog.exporters.map((exporter) => ({
              id: exporter.type,
              text: dialog.optionalL(exporter.title, this)
            }));
          },
          onChange({ value: value2 }) {
            this.owner.widgetMap.alignRowsField.hidden = value2.match(/singlepage/) || value2.match(/^vertical/);
            this.owner.widgetMap.repeatHeaderField.hidden = !value2.match(/multipagevertical/);
          },
          weight: 300
        },
        alignRowsField: {
          type: "checkbox",
          label: "L{ExportDialog.alignRows}",
          name: "alignRows",
          checked: false,
          hidden: true,
          weight: 400
        },
        repeatHeaderField: {
          type: "checkbox",
          label: "L{ExportDialog.repeatHeader}",
          name: "repeatHeader",
          localeClass: this,
          hidden: true,
          weight: 500
        },
        fileFormatField: {
          type: "combo",
          label: "L{ExportDialog.fileFormat}",
          name: "fileFormat",
          localeClass: this,
          editable: false,
          value: "pdf",
          items: [],
          onChange({ value: value2, oldValue }) {
            const dialog = this.parent;
            if (dialog.hidePNGMultipageOption) {
              const exporterField = dialog.widgetMap.exporterTypeField, exporter = exporterField.store.find((r) => r.id === "singlepage");
              if (value2 === FileFormat.png && exporter) {
                this._previousDisabled = exporterField.disabled;
                exporterField.disabled = true;
                this._previousValue = exporterField.value;
                exporterField.value = "singlepage";
              } else if (oldValue === FileFormat.png && this._previousValue) {
                exporterField.disabled = this._previousDisabled;
                exporterField.value = this._previousValue;
              }
            }
          },
          weight: 600
        },
        paperFormatField: {
          type: "combo",
          label: "L{ExportDialog.paperFormat}",
          name: "paperFormat",
          editable: false,
          value: "A4",
          items: [],
          weight: 700
        },
        orientationField: {
          type: "exportorientationcombo",
          label: "L{ExportDialog.orientation}",
          name: "orientation",
          value: "portrait",
          weight: 800
        }
      },
      bbar: {
        defaults: {
          localeClass: this
        },
        items: {
          exportButton: {
            color: "b-green",
            text: "L{ExportDialog.export}",
            weight: 100,
            onClick: "up.onExportClick"
          },
          cancelButton: {
            color: "b-gray",
            text: "L{ExportDialog.cancel}",
            weight: 200,
            onClick: "up.onCancelClick"
          }
        }
      },
      internalListeners: {
        beforeShow: "onInternalBeforeShow"
      }
    };
  }
  //endregion
  construct(config = {}) {
    const me = this, { client } = config;
    if (!client) {
      throw new Error("`client` config is required");
    }
    me.columnsStore = client.columns.chain((column) => column.isLeaf && column.exportable, null, { excludeCollapsedRecords: false });
    me.applyInitialValues(config);
    super.construct(config);
    LocaleManager_default.ion({
      locale: "onLocaleChange",
      prio: -1,
      thisObj: me
    });
  }
  updateUseBrowserPrint(value2) {
    const me = this;
    me.whenVisible(() => {
      const { exportButton, fileFormatField } = me.widgetMap, defaultText = exportButton.initialConfig.text, isDefaultTitle = !("title" in me.initialConfig);
      if (!defaultText || defaultText === "L{ExportDialog.export}" && value2) {
        exportButton.text = value2 ? me.L("L{ExportDialog.print}") : me.L("L{ExportDialog.export}");
      }
      if (isDefaultTitle) {
        me.title = value2 ? me.L("L{printSettings}") : me.L("L{exportSettings}");
      }
      fileFormatField.hidden = value2 || Boolean(fileFormatField.initialConfig.hidden);
    });
  }
  applyInitialValues(config) {
    const me = this, items2 = config.items = config.items || {};
    config.width = config.width || me.L("L{width}");
    config.defaults = config.defaults || {};
    config.defaults.labelWidth = config.defaults.labelWidth || me.L("L{ExportDialog.labelWidth}");
    items2.columnsField = items2.columnsField || {};
    items2.fileFormatField = items2.fileFormatField || {};
    items2.paperFormatField = items2.paperFormatField || {};
    items2.fileFormatField.items = buildComboItems(FileFormat, (value2) => value2.toUpperCase());
    items2.paperFormatField.items = buildComboItems(PaperFormat);
    items2.columnsField.store = me.columnsStore;
  }
  onInternalBeforeShow() {
    const { columnsField, alignRowsField, exporterTypeField, repeatHeaderField } = this.widgetMap;
    if (this.autoSelectVisibleColumns) {
      columnsField.value = this.columnsStore.query((c) => !c.hidden);
    }
    const exporterTypeValue = exporterTypeField.value;
    alignRowsField.hidden = exporterTypeValue.match(/singlepage/) || exporterTypeValue.match(/^vertical/);
    repeatHeaderField.hidden = !exporterTypeValue.match("multipagevertical");
  }
  onLocaleChange() {
    const labelWidth = this.L("L{labelWidth}");
    this.width = this.L("L{width}");
    this.eachWidget((widget) => {
      if (widget instanceof Field) {
        widget.labelWidth = labelWidth;
      }
    });
  }
  onExportClick() {
    const values = this.values;
    this.trigger("export", { values });
  }
  onCancelClick() {
    this.trigger("cancel");
    this.hide();
  }
};
//region Config
__publicField(ExportDialog, "$name", "ExportDialog");
__publicField(ExportDialog, "type", "exportdialog");
ExportDialog.initClass();
ExportDialog._$name = "ExportDialog";

// ../Grid/lib/Grid/feature/export/exporter/ExporterBase.js
var ExporterBase = class extends Delayable_default(Events_default(Localizable_default(Base))) {
  static get defaultConfig() {
    return {
      /**
       * `true` to replace all linked CSS files URLs to absolute before passing HTML to the server.
       * When passing a string the current origin of the CSS files URLS will be replaced by the passed origin.
       *
       * For example: css files pointing to /app.css will be translated from current origin to {translateURLsToAbsolute}/app.css
       * @config {Boolean|String}
       * @default
       */
      translateURLsToAbsolute: true,
      /**
       * When `true` links are converted to absolute by combining current window location (with replaced origin) with
       * resource link.
       * When false links are converted by combining new origin with resource link (for angular)
       * @config {Boolean}
       * @default
       */
      keepPathName: true,
      /**
       * This method accepts all stylesheets (link and style tags) which are supposed to be put on the page. Use this
       * hook method to filter or modify them.
       *
       * ```javascript
       * new Grid({
       *     features: {
       *         pdfExport: {
       *             // filter out inline styles and bootstrap.css
       *             filterStyles: styles => styles.filter(item => !/(link|bootstrap.css)/.test(item))
       *         }
       *     }
       * });
       * ```
       * @param {String[]} styles
       * @returns {String[]} List of stylesheets to put on the exported page
       */
      filterStyles: (styles2) => styles2,
      /**
       * A function that determines when to capture the HTML for exported rows. When set to null (the default),
       * the export happens immediately without waiting. The function receives the export configuration as a
       * parameter.
       * The rendering process works in chunks, using the same approach as the Grid component - displaying visible
       * rows with an additional buffer.
       *
       * Examples:
       * ```javascript
       * // simple timeout
       * new Grid({
       *     features : {
       *         pdfExport : {
       *             rowReadyCondition : async () => await new Promise(resolve => setTimeout(resolve, 100))
       *         }
       *     }
       * });
       *
       * // waiting for a condition
       * new Grid({
       *     features : {
       *         pdfExport : {
       *             rowReadyCondition : async () => await promiseWithCondition
       *         }
       *     }
       * });
       * ```
       * @prp {Function}
       * @returns {void}
       */
      rowReadyCondition: null
    };
  }
  static inchToPx(value2) {
    return value2 * 96;
  }
  /**
   * Template of an extracted page.
   * @param {Object} data Data for the page template
   * @returns {String}
   */
  pageTpl(data) {
    const {
      title,
      header,
      footer,
      styles: styles2,
      htmlClasses,
      bodyClasses = [],
      paperHeight,
      paperWidth,
      html,
      currentPage
    } = data;
    let { htmlStyle = "", bodyStyle = "" } = data;
    bodyClasses.push(`b-${this.constructor.type}`);
    if (DomHelper.scrollBarWidth) {
      bodyClasses.push("b-visible-scrollbar");
    } else {
      bodyClasses.push("b-overlay-scrollbar");
    }
    if (BrowserHelper.isChrome) {
      bodyClasses.push("b-chrome");
    } else if (BrowserHelper.isSafari) {
      bodyClasses.push("b-safari");
    } else if (BrowserHelper.isFirefox) {
      bodyClasses.push("b-firefox");
    }
    bodyStyle = `${bodyStyle}; width: ${paperWidth}in; height: ${paperHeight}in;`;
    return TemplateHelper.tpl`
            <!DOCTYPE html>
            <html class="${htmlClasses} b-print-root" style="${htmlStyle}">
                <head>
                    <meta content="text/html; charset=UTF-8" http-equiv="Content-Type" />
                    <title>${title}</title>
                    ${styles2.join("")}
                </head>
                <body class="b-export b-print ${bodyClasses.join(" ")}" style="${bodyStyle}">
                <div class="b-page-wrap">
                    <div class="b-export-content b-page-${currentPage}">
                        ${header && `<div class="b-export-header" style="width: 100%">${header}</div>`}
                        <div class="b-export-body"><div class="b-export-viewport">${html}</div></div>
                        ${footer && `<div class="b-export-footer" style="width: 100%">${footer}</div>`}
                    </div>
                </div>
                </body>
            </html>`;
  }
  /**
   * Returns all style-related tags: `<style>` and `<link rel="stylesheet">`
   * @property {String[]}
   * @readonly
   */
  get stylesheets() {
    const me = this;
    if (me._stylesheets) {
      return me._stylesheets;
    }
    const translate = me.translateURLsToAbsolute, origin = globalThis.origin, styleSheetNodes = Array.from(document.querySelectorAll('link[rel="stylesheet"], style')), styles2 = [];
    styleSheetNodes.forEach((node) => {
      node = node.cloneNode(true);
      if (translate && node.href) {
        let result;
        if (translate === true) {
          result = node.href;
        } else if (this.keepPathName) {
          result = node.href.replace(origin, translate);
        } else {
          result = new URL(node.getAttribute("href"), translate);
        }
        node.setAttribute("href", result);
      }
      let styleText = node.outerHTML;
      if (translate && /style/i.test(node.tagName)) {
        const converter = me.getStyleTagURLConverter(translate);
        styleText = styleText.replace(/url\(['"]?(.+?)['"]?\)/g, converter);
      }
      styles2.push(styleText);
    });
    return me._stylesheets = me.filterStyles(styles2);
  }
  set stylesheets(value2) {
    this._stylesheets = value2 ? this.filterStyles(value2) : value2;
  }
  getStyleTagURLConverter(translate) {
    return function(match, url) {
      let result;
      try {
        let base;
        if (/^#/.test(url)) {
          result = match;
        } else {
          if (translate === true) {
            base = globalThis.location.href;
          } else if (this.keepPathName) {
            base = globalThis.location.href.replace(globalThis.location.origin, translate);
          } else {
            base = translate;
          }
          result = `url('${new URL(url, base).href}')`;
        }
      } catch (e) {
        result = match;
      }
      return result;
    }.bind(this);
  }
  saveState({ client }) {
    const { disableGridColumnIdWarning } = client;
    client.disableGridColumnIdWarning = true;
    this.state = client.state;
    client.disableGridColumnIdWarning = disableGridColumnIdWarning;
  }
  async restoreState({ client }) {
    const promises = [], detachers = /* @__PURE__ */ new Set();
    detachers.add(
      client.scrollable.ion({
        scroll() {
          promises.push(client.scrollable.await("scrollEnd"));
        }
      })
    );
    client.eachSubGrid(({ header, scrollable }) => {
      detachers.add(
        scrollable.ion({
          scroll() {
            promises.push(scrollable.await("scrollEnd"));
          }
        })
      );
      detachers.add(
        header.scrollable.ion({
          scroll() {
            promises.push(header.scrollable.await("scrollEnd"));
          }
        })
      );
    });
    client.state = this.state;
    await Promise.all(promises);
    detachers.forEach((fn2) => fn2());
  }
  beforeExport() {
    this.element = document.createElement("div");
  }
  //region DOM helpers
  cloneElement(element, target = this.element, clear = true) {
    if (clear) {
      target.innerHTML = "";
    }
    target.appendChild(element.cloneNode(true));
    const maskEl = target.querySelector(".b-gridbase > .b-mask");
    maskEl == null ? void 0 : maskEl.remove();
  }
  createPlaceholder(el, clear = true, config = {}) {
    if (clear) {
      el.innerHTML = "";
    }
    return DomHelper.createElement(Object.assign({
      parent: el,
      id: IdHelper.generateId("export")
    }, config));
  }
  prepareElement({ client }) {
    const { tbar, bbar } = client;
    if (tbar) {
      this.element.querySelector(`#${tbar.id}`).remove();
    }
    if (bbar) {
      this.element.querySelector(`#${bbar.id}`).remove();
    }
  }
  /**
   * Appends generated header/footer element to the document body to measure their height
   * @param {String} html
   * @param {String} wrapperCls
   * @returns {Number}
   * @internal
   */
  measureElement(html = "", wrapperCls = "") {
    if (html instanceof HTMLElement) {
      html = html.outerHTML;
    }
    const target = DomHelper.createElement({
      parent: document.body,
      style: {
        visibility: "hidden",
        position: "absolute"
      },
      className: wrapperCls,
      // Add html to measure to a div between two other divs to take margin into account
      html: `<div style="height: 1px"></div>${html}<div style="height: 1px"></div>`
    });
    const result = target.offsetHeight - 2;
    target.remove();
    return result;
  }
  // Converts local urls to absolute
  prepareHTML(html) {
    if (html instanceof HTMLElement) {
      html = html.outerHTML;
    }
    const target = DomHelper.createElement({
      parent: document.body,
      style: {
        visibility: "hidden",
        position: "absolute"
      },
      html
    }), elements = target.querySelectorAll("img");
    for (let i = 0, l = elements.length; i < l; i++) {
      elements[i].setAttribute("src", elements[i].src);
    }
    const result = target.innerHTML;
    target.remove();
    return result;
  }
  getVirtualScrollerHeight(client) {
    let result = 0;
    client.eachSubGrid((subGrid) => {
      if (subGrid.overflowingHorizontally) {
        result = DomHelper.scrollBarWidth;
      }
    });
    return result === 0 ? result : result + 1;
  }
  //endregion
  // Use carefully picked values for portrait/landscape mode and fallback to legacy width/height in case use
  // has them overridden
  getPaperWidth(paperFormat, isPortrait) {
    if ("portraitWidth" in paperFormat) {
      const prop = isPortrait ? "portraitWidth" : "landscapeWidth";
      return paperFormat[prop] - (this.exportMeta.isPrinting ? getPrintPaperSizeAdjustments()[prop] : 0);
    } else {
      return isPortrait ? paperFormat.width : paperFormat.height;
    }
  }
  getPaperHeight(paperFormat, isPortrait) {
    if ("portraitHeight" in paperFormat) {
      const prop = isPortrait ? "portraitHeight" : "landscapeHeight";
      return paperFormat[prop] - (this.exportMeta.isPrinting ? getPrintPaperSizeAdjustments()[prop] : 0);
    } else {
      return isPortrait ? paperFormat.height : paperFormat.width;
    }
  }
  inchToPx(value2) {
    return value2 * 96;
  }
  getScaleValue(base, value2) {
    return Math.floor(base * 1e4 / value2) / 1e4;
  }
  async export(config) {
    const me = this;
    let pages;
    me.beforeExport();
    me.saveState(config);
    await me.prepareComponent(config);
    try {
      pages = await me.getPages(config);
    } finally {
      await me.restoreComponent(config);
      me.stylesheets = null;
      await AsyncHelper.animationFrame();
      await me.restoreState(config);
    }
    return pages;
  }
  async getPages(config) {
    const generator = this.pagesExtractor(config), pages = [];
    let step;
    while ((step = await generator.next()) && !step.done) {
      pages.push(step.value);
    }
    return pages;
  }
  prepareExportElement() {
    const me = this, { element, exportMeta } = me;
    Object.values(exportMeta.subGrids).forEach(({ width, id: id2, headerId, footerId }) => {
      [id2, headerId, footerId].forEach((id3) => {
        if (id3) {
          const childElement = element.querySelector(`[id="${id3}"]`);
          if (childElement) {
            childElement.style.width = `${width}px`;
            childElement.style.flex = "";
          }
        }
      });
    });
    return element.innerHTML;
  }
  async prepareComponent(config) {
    const me = this, {
      client,
      columns,
      keepRegionSizes,
      useRenderedColumnWidth
    } = config, { rowManager } = client, exportMeta = me.exportMeta = {
      totalWidth: 0,
      totalHeight: 0,
      subGrids: {},
      isPrinting: config.useBrowserPrint,
      title: config.title || client.description
    }, paperFormat = PaperFormat[config.paperFormat], isPortrait = config.orientation === Orientation.portrait, paperWidth = me.getPaperWidth(paperFormat, isPortrait), paperHeight = me.getPaperHeight(paperFormat, isPortrait), pageWidth = me.inchToPx(paperWidth), pageHeight = me.inchToPx(paperHeight);
    Object.assign(exportMeta, {
      paperWidth,
      paperHeight,
      realPaperWidth: me.getPaperWidth(paperFormat, isPortrait),
      realPaperHeight: me.getPaperHeight(paperFormat, isPortrait),
      pageWidth,
      pageHeight
    });
    client.columns.forEach((column) => {
      if (columns.includes(column.id)) {
        column.show();
      } else {
        column.hide();
      }
    });
    await AsyncHelper.animationFrame();
    const { element } = me;
    me.cloneElement(client.element);
    me.prepareElement(config);
    client.eachSubGrid((subGrid) => {
      var _a5, _b2, _c2;
      const placeHolder = me.createPlaceholder(element.querySelector(`[id="${subGrid.id}"]`), false);
      let width;
      if (keepRegionSizes == null ? void 0 : keepRegionSizes[subGrid.region]) {
        width = subGrid.element.offsetWidth;
      } else {
        const { visibleColumns } = subGrid.columns;
        if (
          // If there is only one visible column...
          visibleColumns.length === 1 && // ...and it is not a timeaxis column, which should always take as much space as possible
          !visibleColumns[0].isTimeAxisColumn && // ...and originally in grid there was only one visible column and it is the same one
          me.state.columns.filter((c) => c.region === subGrid.region && !c.hidden && c.id !== visibleColumns[0].id).length === 0
        ) {
          width = subGrid.element.offsetWidth;
        } else {
          width = subGrid.columns.visibleColumns.reduce((result, column) => {
            if (!useRenderedColumnWidth && typeof column.width === "number") {
              result += column.width;
            } else {
              result += (client.hideHeaders ? rowManager.rows[0].getCell(column.id) : column.element).offsetWidth;
            }
            return result;
          }, 0);
        }
      }
      exportMeta.totalWidth += width;
      const splitterWidth2 = ((_a5 = subGrid.splitterElement) == null ? void 0 : _a5.offsetWidth) || 0;
      exportMeta.totalWidth += splitterWidth2;
      exportMeta.subGrids[subGrid.region] = {
        id: subGrid.id,
        headerId: ((_b2 = subGrid.header) == null ? void 0 : _b2.id) || null,
        footerId: ((_c2 = subGrid.footer) == null ? void 0 : _c2.id) || null,
        rows: [],
        splitterWidth: splitterWidth2,
        placeHolder,
        width
      };
    });
  }
  async restoreComponent(config) {
  }
  collectRow(row) {
    const subGrids = this.exportMeta.subGrids, re = /data-owner-cmp=".+?"/, rowExpanderFeature = row.grid.features.rowExpander;
    Object.entries(row.elements).forEach(([key, value2]) => {
      value2.classList.remove("b-export-row");
      let { outerHTML } = value2;
      if (rowExpanderFeature) {
        const expanderContainer = value2.querySelector(`.${rowExpanderFeature.shadowRootContainerClass}`);
        if (expanderContainer) {
          const nodes = [];
          expanderContainer._shadowRoot.childNodes.forEach((node) => {
            if (node.tagName !== "LINK" && node.tagName !== "STYLE") {
              nodes.push(node.outerHTML);
            }
          });
          outerHTML = outerHTML.replace(
            expanderContainer.outerHTML,
            expanderContainer.outerHTML.replace(/></, `>${nodes.join("")}<`)
          );
        }
      }
      subGrids[key].rows.push(outerHTML.replace(re, ""));
      value2.classList.add("b-export-row");
    });
  }
};
ExporterBase.prototype.pagesExtractor = async function* pagesExtractor() {
  throw new Error("Implement this method in a subclass");
};
ExporterBase._$name = "ExporterBase";

// ../Grid/lib/Grid/feature/export/exporter/Exporter.js
var Exporter = class extends ExporterBase {
  cloneElement(element, target = this.element, clear = true) {
    super.cloneElement(element, target, clear);
    DomHelper.removeEachSelector(target, ".b-grid-row,.b-grid-merged-cells-container");
  }
  getVisibleRowsCount(client) {
    const rowManager = client.rowManager, firstVisibleIndex = rowManager.rows.indexOf(rowManager.firstVisibleRow), lastVisibleIndex = rowManager.rows.indexOf(rowManager.lastVisibleRow);
    let result;
    if (firstVisibleIndex !== -1) {
      if (lastVisibleIndex === -1) {
        result = client.store.count - firstVisibleIndex;
      } else {
        result = lastVisibleIndex - firstVisibleIndex + 1;
      }
    } else {
      result = client.store.count;
    }
    return result;
  }
  async prepareComponent(config) {
    const { client } = config, { rowManager } = client, fakeRowCount = this.fakeRowCount || client.rowManager.rowCount;
    await super.prepareComponent(config);
    const fakeRows = this.exportMeta.fakeRows = [];
    for (let i = 0; i < fakeRowCount; i++) {
      fakeRows.push(rowManager.rowClass.new({
        cls: {
          [client.rowCls]: 1,
          "b-export-row": 1
        },
        rowManager,
        grid: client,
        // use fake indices, they aren't really required
        index: -10,
        dataIndex: -10,
        isFakeRow: true
      }));
    }
    this.exportMeta.rowsMap = /* @__PURE__ */ new Map();
    if (fakeRows.length) {
      client.eachSubGrid((subGrid) => {
        subGrid.onAddRow({ rows: fakeRows });
      });
    }
    fakeRows.forEach((fakeRow) => fakeRow.element.dataset.ownerCmp = client.id);
  }
  async restoreComponent(config) {
    await super.restoreComponent(config);
    if (this.exportMeta.fakeRows) {
      this.exportMeta.fakeRows.forEach((fakeRow) => fakeRow.destroy());
      delete this.exportMeta.fakeRows;
    }
  }
  renderMergedCells(config, fromIndex, toIndex, rows) {
    const me = this, { client } = config, { subGrids } = me.exportMeta, domConfigMap = client.features.mergeCells.buildMergedCellsConfig(fromIndex, toIndex, rows);
    for (const subGridName in subGrids) {
      const subGrid = subGrids[subGridName], target = document.createElement("div");
      DomSync.sync({
        targetElement: target,
        domConfig: {
          children: domConfigMap[subGridName].children
        }
      });
      if (target.childNodes.length) {
        target.childNodes.forEach((child) => {
          const { syncId } = child.dataset, range = client.features.mergeCells.mergedRanges.find((range2) => {
            var _a5;
            return ((_a5 = range2.cellElement) == null ? void 0 : _a5.parentNode.dataset.syncId) === syncId;
          });
          if (range) {
            child.innerHTML = range.cellElement.outerHTML;
          }
        });
        subGrid.mergedCellsHtml = [target.innerHTML];
      }
    }
  }
};
__publicField(Exporter, "configurable", {
  fakeRowCount: null
});
Exporter._$name = "Exporter";

// ../Grid/lib/Grid/feature/export/exporter/MultiPageExporter.js
var MultiPageExporter = class extends Exporter {
  static get title() {
    return this.L("L{multipage}");
  }
  static get exportingPageText() {
    return "L{exportingPage}";
  }
  //region State management
  async stateNextPage({ client, rowsRange }) {
    const { exportMeta } = this;
    ++exportMeta.currentPage;
    ++exportMeta.verticalPosition;
    if (exportMeta.lastRowIndex >= exportMeta.finishRowIndex) {
      exportMeta.verticalPages = exportMeta.verticalPosition;
      exportMeta.totalPages = exportMeta.verticalPages * exportMeta.horizontalPages;
    } else {
      exportMeta.verticalPages = Math.max(exportMeta.verticalPages, exportMeta.verticalPosition + 1);
    }
    if (exportMeta.verticalPosition >= exportMeta.verticalPages && exportMeta.horizontalPosition < exportMeta.horizontalPages - 1) {
      Object.assign(exportMeta, {
        verticalPosition: 0,
        horizontalPosition: exportMeta.horizontalPosition + 1,
        lastTop: 0,
        lastRowIndex: rowsRange === RowsRange.visible ? client.rowManager.firstVisibleRow.dataIndex : 0,
        lastRowExported: false
      });
    }
  }
  //endregion
  //region Preparation
  async prepareComponent(config) {
    await super.prepareComponent(config);
    const me = this, { exportMeta } = me, {
      client,
      headerTpl,
      footerTpl,
      alignRows,
      rowsRange
    } = config, paperFormat = PaperFormat[config.paperFormat], isPortrait = config.orientation === Orientation.portrait, paperWidth = me.getPaperWidth(paperFormat, isPortrait), paperHeight = me.getPaperHeight(paperFormat, isPortrait), pageWidth = me.inchToPx(paperWidth), pageHeight = me.inchToPx(paperHeight), onlyVisibleRows = rowsRange === RowsRange.visible, horizontalPages = Math.ceil(exportMeta.totalWidth / pageWidth);
    let contentHeight = pageHeight;
    if (headerTpl) {
      contentHeight -= me.measureElement(headerTpl({
        totalWidth: exportMeta.totalWidth,
        totalPages: -1,
        currentPage: -1
      }), "b-export-header");
    }
    if (footerTpl) {
      contentHeight -= me.measureElement(footerTpl({
        totalWidth: exportMeta.totalWidth,
        totalPages: -1,
        currentPage: -1
      }), "b-export-footer");
    }
    let totalHeight, verticalPages, totalRows = client.store.count;
    if (onlyVisibleRows) {
      totalRows = me.getVisibleRowsCount(client);
      totalHeight = exportMeta.totalHeight + client.headerHeight + client.footerHeight + client.bodyHeight;
      exportMeta.finishRowIndex = client.rowManager.lastVisibleRow.dataIndex;
    } else {
      totalHeight = exportMeta.totalHeight + client.headerHeight + client.footerHeight + client.scrollable.scrollHeight;
      exportMeta.finishRowIndex = client.store.count - 1;
    }
    if (alignRows && !onlyVisibleRows) {
      const rowHeight = client.rowManager.rowOffsetHeight, rowsOnFirstPage = Math.floor((contentHeight - client.headerHeight) / rowHeight), rowsPerPage = Math.floor(contentHeight / rowHeight), remainingRows = totalRows - rowsOnFirstPage;
      verticalPages = 1 + Math.ceil(remainingRows / rowsPerPage);
    } else {
      verticalPages = Math.ceil(totalHeight / contentHeight);
    }
    Object.assign(exportMeta, {
      paperWidth,
      paperHeight,
      pageWidth,
      pageHeight,
      horizontalPages,
      verticalPages,
      totalHeight,
      contentHeight,
      totalRows,
      totalPages: horizontalPages * verticalPages,
      currentPage: 0,
      verticalPosition: 0,
      horizontalPosition: 0,
      lastTop: 0,
      lastRowIndex: onlyVisibleRows ? client.rowManager.firstVisibleRow.dataIndex : 0,
      lastRowExported: false
    });
  }
  //endregion
  async renderRows(config) {
    const me = this, { exportMeta } = me, {
      client,
      alignRows,
      repeatHeader
    } = config, {
      verticalPosition,
      contentHeight,
      lastRowIndex,
      fakeRows,
      rowsMap
    } = exportMeta, rows = [], clientHeaderHeight = repeatHeader ? 0 : client.headerHeight, { store } = client, hasMergeCells = client.hasActiveFeature("mergeCells");
    let index = lastRowIndex + (!alignRows || verticalPosition === 0 ? 0 : 1), lastTop = 0, pageBreak = false, remainingHeight = contentHeight, previousTop, lastFakeRowIndex, lastFakeRow;
    if (verticalPosition === 0) {
      remainingHeight -= clientHeaderHeight;
    }
    while (remainingHeight > 0 && !pageBreak) {
      let i = 0;
      for (; i < fakeRows.length && remainingHeight > 0; i++, index++) {
        const record = store.getAt(index), fakeRow = fakeRows[i];
        fakeRow.render(index, record, true, false, true);
        if (alignRows && remainingHeight < fakeRow.offsetHeight) {
          pageBreak = true;
          break;
        } else {
          previousTop = lastTop;
          lastTop = fakeRow.translate(lastTop);
          exportMeta.lastRowIndex = fakeRow.dataIndex;
          remainingHeight -= fakeRow.offsetHeight;
          const rowConfig = {
            top: fakeRow.top,
            bottom: fakeRow.bottom,
            offsetHeight: fakeRow.offsetHeight,
            dataIndex: fakeRow.dataIndex
          };
          rows.push(rowConfig);
          rowsMap.set(record.id, rowConfig);
          lastFakeRow = fakeRow;
          lastFakeRowIndex = i;
          if (fakeRow.dataIndex === exportMeta.finishRowIndex) {
            if (remainingHeight >= 0) {
              exportMeta.lastRowExported = true;
              pageBreak = true;
              break;
            }
          }
        }
      }
      const rowsBatch = fakeRows.slice(0, lastFakeRowIndex + 1);
      if (config.rowReadyCondition) {
        await me.rowReadyCondition(config, {
          fromIndex: rowsBatch[0].dataIndex,
          toIndex: rowsBatch[rowsBatch.length - 1].dataIndex,
          fakeRows: rowsBatch
        });
      }
      rowsBatch.forEach((fakeRow) => me.collectRow(fakeRow));
    }
    if (hasMergeCells) {
      me.renderMergedCells(config, lastRowIndex, index, rows);
    }
    exportMeta.lastTop = alignRows ? lastTop : previousTop;
    exportMeta.exactGridHeight = lastFakeRow.bottom + client.footerContainer.offsetHeight + (verticalPosition === 0 || repeatHeader ? clientHeaderHeight : 0);
    await me.onRowsCollected(rows, config);
  }
  async buildPage(config) {
    const me = this, { exportMeta } = me, {
      client,
      headerTpl,
      footerTpl
    } = config, {
      totalWidth,
      totalPages,
      currentPage,
      subGrids
    } = exportMeta;
    Object.values(subGrids).forEach((subGrid) => subGrid.rows = []);
    if (config.rowsRange === RowsRange.all) {
      exportMeta.totalHeight = client.height - client.bodyHeight + client.scrollable.scrollHeight - me.getVirtualScrollerHeight(client);
    }
    let header, footer;
    if (headerTpl) {
      header = me.prepareHTML(headerTpl({
        totalWidth,
        totalPages,
        currentPage
      }));
    }
    if (footerTpl) {
      footer = me.prepareHTML(footerTpl({
        totalWidth,
        totalPages,
        currentPage
      }));
    }
    await me.renderRows(config);
    const html = me.buildPageHtml(config);
    return { html, header, footer };
  }
  async onRowsCollected() {
  }
  buildPageHtml() {
    const me = this, { subGrids } = me.exportMeta;
    let html = me.prepareExportElement();
    Object.values(subGrids).forEach(({ placeHolder, rows, mergedCellsHtml }) => {
      const placeHolderText = placeHolder.outerHTML;
      let contentHtml = rows.join("");
      if (mergedCellsHtml == null ? void 0 : mergedCellsHtml.length) {
        contentHtml += `<div class="b-grid-merged-cells-container">${mergedCellsHtml.join("")}</div>`;
      }
      html = html.replace(placeHolderText, contentHtml);
    });
    return html;
  }
};
__publicField(MultiPageExporter, "$name", "MultiPageExporter");
__publicField(MultiPageExporter, "type", "multipage");
MultiPageExporter.prototype.pagesExtractor = async function* pagesExtractor2(config) {
  const me = this, {
    exportMeta,
    stylesheets
  } = me, {
    totalWidth,
    totalPages,
    paperWidth,
    paperHeight,
    realPaperWidth,
    realPaperHeight,
    title
  } = exportMeta;
  let currentPage;
  while (!exportMeta.lastRowExported) {
    currentPage = exportMeta.currentPage;
    me.trigger("exportStep", {
      text: me.L(MultiPageExporter.exportingPageText, { currentPage, totalPages }),
      progress: Math.round((currentPage + 1) / totalPages * 90)
    });
    const { html, header, footer } = await me.buildPage(config);
    const styles2 = [
      ...stylesheets,
      `
                <style>
                    .b-page-wrap {
                        width: ${paperWidth}in;
                        height: ${paperHeight}in;
                    }
                    .b-page-${currentPage} #${config.client.id} {
                        height: ${exportMeta.exactGridHeight}px !important;
                        width: ${totalWidth}px !important;
                    }
                    .b-page-${currentPage} .b-export-body .b-export-viewport {
                        transform: translate(${-paperWidth * exportMeta.horizontalPosition}in, 0);
                    }
                    ${exportMeta.verticalPosition === 0 ? "" : `.b-page-${currentPage} .b-grid-header-container {
                        display: none;
                    }`}
                </style>
            `
    ];
    await me.stateNextPage(config);
    yield {
      html: me.pageTpl({
        html,
        title,
        header,
        footer,
        styles: styles2,
        paperWidth,
        paperHeight,
        realPaperWidth,
        realPaperHeight,
        currentPage
      })
    };
  }
};
MultiPageExporter._$name = "MultiPageExporter";

// ../Grid/lib/Grid/feature/export/exporter/MultiPageVerticalExporter.js
var MultiPageVerticalExporter = class extends Exporter {
  static get title() {
    return this.L("L{multipagevertical}");
  }
  static get exportingPageText() {
    return "L{exportingPage}";
  }
  //region State management
  async stateNextPage() {
    const { exportMeta } = this, {
      totalRows,
      processedRows,
      totalPages
    } = exportMeta;
    ++exportMeta.currentPage;
    ++exportMeta.verticalPosition;
    if (exportMeta.currentPage === totalPages && processedRows.size !== totalRows) {
      ++exportMeta.totalPages;
      ++exportMeta.verticalPages;
    }
  }
  //endregion
  calculateScale() {
    const me = this, {
      exportMeta
    } = me, {
      pageWidth,
      totalWidth
    } = exportMeta, scale = me.getScaleValue(pageWidth, totalWidth);
    me.exportMeta.scale = scale;
    return scale;
  }
  estimateTotalPages(config) {
    const me = this, { exportMeta } = me, {
      client,
      headerTpl,
      footerTpl,
      alignRows,
      rowsRange,
      repeatHeader
    } = config, {
      pageWidth,
      pageHeight,
      totalWidth
    } = exportMeta, scale = me.calculateScale(pageWidth, totalWidth);
    let totalRows = client.store.count, totalHeight = totalRows ? 0 - me.getVirtualScrollerHeight(client) + client.height - client.bodyElement.offsetHeight + client.scrollable.scrollHeight : 0, contentHeight = pageHeight / scale, rowsHeight = totalHeight, verticalPages;
    if (headerTpl) {
      contentHeight -= me.measureElement(headerTpl({
        totalWidth,
        totalPages: -1,
        currentPage: -1
      }), "b-export-header");
    }
    if (footerTpl) {
      contentHeight -= me.measureElement(footerTpl({
        totalWidth,
        totalPages: -1,
        currentPage: -1
      }), "b-export-footer");
    }
    if (repeatHeader) {
      contentHeight -= client.headerHeight + client.footerHeight;
      totalHeight -= client.headerHeight + client.footerHeight;
    }
    if (rowsRange === RowsRange.visible) {
      const rowManager = client.rowManager, firstRow = rowManager.firstVisibleRow, lastRow = rowManager.lastVisibleRow;
      totalRows = me.getVisibleRowsCount(client);
      rowsHeight = lastRow.bottom - firstRow.top;
      totalHeight = client.headerHeight + client.footerHeight + rowsHeight;
      exportMeta.lastRowIndex = firstRow.dataIndex;
      exportMeta.finishRowIndex = lastRow.dataIndex;
    } else {
      exportMeta.finishRowIndex = client.store.count - 1;
    }
    if (alignRows && !repeatHeader && rowsRange !== RowsRange.visible) {
      const rowHeight = client.rowManager.rowOffsetHeight, rowsOnFirstPage = Math.floor((contentHeight - client.headerHeight) / rowHeight), rowsPerPage = Math.floor(contentHeight / rowHeight), remainingRows = totalRows - rowsOnFirstPage;
      verticalPages = 1 + Math.ceil(remainingRows / rowsPerPage);
    } else {
      verticalPages = Math.ceil(rowsHeight / contentHeight);
    }
    Object.assign(exportMeta, {
      scale,
      contentHeight,
      totalRows,
      totalHeight,
      verticalPages,
      initialScroll: 0,
      horizontalPages: 1,
      totalPages: verticalPages,
      lastRowExported: false
    });
  }
  async prepareComponent(config) {
    await super.prepareComponent(config);
    const me = this, { exportMeta } = me, paperFormat = PaperFormat[config.paperFormat], isPortrait = config.orientation === Orientation.portrait, paperWidth = me.getPaperWidth(paperFormat, isPortrait), paperHeight = me.getPaperHeight(paperFormat, isPortrait), pageWidth = me.inchToPx(paperWidth), pageHeight = me.inchToPx(paperHeight), horizontalPages = 1;
    Object.assign(exportMeta, {
      paperWidth,
      paperHeight,
      realPaperWidth: me.getPaperWidth(paperFormat, isPortrait),
      realPaperHeight: me.getPaperHeight(paperFormat, isPortrait),
      pageWidth,
      pageHeight,
      horizontalPages,
      currentPage: 0,
      verticalPosition: 0,
      horizontalPosition: 0,
      lastTop: 0,
      lastRowIndex: -1,
      processedRows: /* @__PURE__ */ new Set()
    });
    me.estimateTotalPages(config);
  }
  async renderRows(config) {
    const me = this, { exportMeta } = me, {
      client,
      alignRows,
      repeatHeader
    } = config, {
      verticalPosition,
      contentHeight,
      lastRowIndex,
      fakeRows,
      rowsMap,
      nextPageTop = 0
    } = exportMeta, rows = [], clientHeaderHeight = repeatHeader || verticalPosition > 0 ? 0 : client.headerHeight, { store } = client, hasMergeCells = client.hasActiveFeature("mergeCells");
    let index = Math.max(0, lastRowIndex) + (nextPageTop > 0 || !alignRows || verticalPosition === 0 ? 0 : 1), remainingHeight = contentHeight + nextPageTop, lastTop = -nextPageTop, hasHeader = false, i = 0, lastFakeRowIndex = 0, pageBreak = false, lastRowBottom;
    exportMeta.nextPageTop = 0;
    if (verticalPosition === 0) {
      hasHeader = true;
      remainingHeight -= clientHeaderHeight;
    }
    while (remainingHeight > 0 && !pageBreak) {
      lastFakeRowIndex = -1;
      i = 0;
      for (; i < fakeRows.length && remainingHeight > 0; i++, index++) {
        const record = store.getAt(index), fakeRow = fakeRows[i];
        fakeRow.render(index, record, true, false, true);
        const { offsetHeight } = fakeRow;
        if (alignRows && offsetHeight < contentHeight && remainingHeight < offsetHeight) {
          pageBreak = true;
          break;
        } else {
          lastTop = fakeRow.translate(lastTop);
          remainingHeight -= offsetHeight;
          lastRowBottom = fakeRow.bottom;
          if (lastRowBottom > contentHeight && offsetHeight > contentHeight) {
            exportMeta.nextPageTop = contentHeight - fakeRow.top - (hasHeader ? clientHeaderHeight : 0);
            remainingHeight = contentHeight - lastRowBottom;
          }
          exportMeta.lastRowIndex = fakeRow.dataIndex;
          const rowConfig = {
            top: fakeRow.top,
            bottom: lastRowBottom,
            dataIndex: fakeRow.dataIndex,
            offsetHeight
          };
          rows.push(rowConfig);
          rowsMap.set(record.id, rowConfig);
          lastFakeRowIndex = i;
          if (remainingHeight > 0) {
            exportMeta.processedRows.add(index);
          }
          if (fakeRow.dataIndex === exportMeta.finishRowIndex) {
            if (remainingHeight >= 0) {
              exportMeta.lastRowExported = true;
              pageBreak = true;
              break;
            }
            exportMeta.verticalPages = exportMeta.verticalPosition;
            exportMeta.totalPages = exportMeta.verticalPages & exportMeta.horizontalPages;
            me.calculateScale();
          }
        }
      }
      const rowsBatch = fakeRows.slice(0, lastFakeRowIndex + 1);
      if (config.rowReadyCondition) {
        await me.rowReadyCondition(config, {
          fromIndex: rowsBatch[0].dataIndex,
          toIndex: rowsBatch[rowsBatch.length - 1].dataIndex,
          fakeRows: rowsBatch
        });
      }
      rowsBatch.forEach((fakeRow) => me.collectRow(fakeRow));
    }
    if (hasMergeCells) {
      me.renderMergedCells(config, lastRowIndex, index, rows);
    }
    exportMeta.exactGridHeight = lastRowBottom + client.footerContainer.offsetHeight + (verticalPosition === 0 || repeatHeader ? client.headerHeight : 0);
    await me.onRowsCollected(rows, config);
  }
  async buildPage(config) {
    const me = this, { exportMeta } = me, {
      headerTpl,
      footerTpl
    } = config, {
      totalWidth,
      totalPages,
      currentPage,
      subGrids
    } = exportMeta;
    Object.values(subGrids).forEach((subGrid) => subGrid.rows = []);
    let header, footer;
    if (headerTpl) {
      header = me.prepareHTML(headerTpl({
        totalWidth,
        totalPages,
        currentPage
      }));
    }
    if (footerTpl) {
      footer = me.prepareHTML(footerTpl({
        totalWidth,
        totalPages,
        currentPage
      }));
    }
    await me.renderRows(config);
    const html = me.buildPageHtml(config);
    return { html, header, footer };
  }
  async onRowsCollected() {
  }
  buildPageHtml() {
    const me = this, { subGrids } = me.exportMeta;
    let html = me.prepareExportElement();
    Object.values(subGrids).forEach(({ placeHolder, rows, mergedCellsHtml }) => {
      const placeHolderText = placeHolder.outerHTML;
      let contentHtml = rows.join("");
      if (mergedCellsHtml == null ? void 0 : mergedCellsHtml.length) {
        contentHtml += `<div class="b-grid-merged-cells-container">${mergedCellsHtml.join("")}</div>`;
      }
      html = html.replace(placeHolderText, contentHtml);
    });
    return html;
  }
};
__publicField(MultiPageVerticalExporter, "$name", "MultiPageVerticalExporter");
__publicField(MultiPageVerticalExporter, "type", "multipagevertical");
MultiPageVerticalExporter.prototype.pagesExtractor = async function* pagesExtractor3(config) {
  const me = this, {
    exportMeta,
    stylesheets
  } = me, {
    totalWidth,
    paperWidth,
    paperHeight,
    realPaperWidth,
    realPaperHeight,
    title
  } = exportMeta, paperSize = config.lockPaperSize && isPaperSizeSupported(config.paperFormat) ? `@page { size: ${config.paperFormat} ${config.orientation} }` : "";
  let { totalPages } = exportMeta, currentPage, style;
  while (!exportMeta.lastRowExported) {
    currentPage = exportMeta.currentPage;
    me.trigger("exportStep", {
      text: me.L(MultiPageVerticalExporter.exportingPageText, { currentPage, totalPages }),
      progress: Math.round((currentPage + 1) / totalPages * 90)
    });
    const { html, header, footer } = await me.buildPage(config);
    const { scale } = me.exportMeta;
    style = `
            ${paperSize}
            .b-page-wrap {
                width: ${realPaperWidth}in;
                height: ${realPaperHeight}in;
            }
            .b-print .b-export-content {
                scale: ${scale};
                transform-origin: top left;
                height: calc(100% / ${scale});
                width: calc(100% / ${scale});
            }
        `;
    if (config.repeatHeader) {
      style = `
                ${style}
                .b-page-${currentPage} #${config.client.id} {
                    height: ${exportMeta.exactGridHeight}px !important;
                    width: ${totalWidth}px !important;
                }
                .b-export-body {
                    height: 100%;
                    display: flex;
                }
                .b-export-viewport {
                    height: 100%;
                }
            `;
    } else {
      style = `
                ${style}
                .b-page-${currentPage} #${config.client.id} {
                    height: ${exportMeta.exactGridHeight}px !important;
                    width: ${totalWidth}px !important;
                }
                ${currentPage === 0 ? "" : `.b-page-${currentPage} .b-grid-header-container {
                    display: none;
                }`}
                .b-export-body {
                    overflow: hidden;
                }
            `;
    }
    const styles2 = [
      ...stylesheets,
      `<style>${style}</style>`
    ];
    await me.stateNextPage();
    ({ totalPages } = exportMeta);
    yield {
      html: me.pageTpl({
        html,
        title,
        header,
        footer,
        styles: styles2,
        paperWidth,
        paperHeight,
        realPaperWidth,
        realPaperHeight,
        currentPage
      })
    };
  }
};
MultiPageVerticalExporter._$name = "MultiPageVerticalExporter";

// ../Grid/lib/Grid/feature/export/exporter/SinglePageExporter.js
var SinglePageExporter = class extends Exporter {
  static get title() {
    return this.localize("L{singlepage}");
  }
  static get defaultConfig() {
    return {
      /**
       * Set to `true` to center content horizontally on the page
       * @config {Boolean}
       */
      centerContentHorizontally: false
    };
  }
  async prepareComponent(config) {
    await super.prepareComponent(config);
    Object.assign(this.exportMeta, {
      verticalPages: 1,
      horizontalPages: 1,
      totalPages: 1,
      currentPage: 0,
      verticalPosition: 0,
      horizontalPosition: 0
    });
  }
  async onRowsCollected() {
  }
  async renderRows(config) {
    const me = this, { client, rowsRange } = config, { rowManager, store } = client, hasMergeCells = client.hasActiveFeature("mergeCells"), onlyVisibleRows = rowsRange === RowsRange.visible, { fakeRows, rowsMap } = me.exportMeta;
    let { totalHeight } = me.exportMeta;
    if (store.count && fakeRows.length) {
      const { firstVisibleRow } = rowManager, fromIndex = onlyVisibleRows ? firstVisibleRow.dataIndex : 0, toIndex = onlyVisibleRows ? rowManager.lastVisibleRow.dataIndex : store.count - 1, rows = [];
      let top = 0;
      if (fakeRows[0].cells.length) {
        for (let i = fromIndex; i <= toIndex; ) {
          let j;
          for (j = 0; j < fakeRows.length && i <= toIndex; ) {
            const record = store.getAt(i), fakeRow = fakeRows[j];
            fakeRow.render(i, record, true, false, true);
            top = fakeRow.translate(top);
            const rowConfig = {
              top: fakeRow.top,
              bottom: fakeRow.bottom,
              offsetHeight: fakeRow.offsetHeight,
              dataIndex: fakeRow.dataIndex
            };
            rows.push(rowConfig);
            rowsMap.set(record.id, rowConfig);
            i++;
            j++;
          }
          if (config.rowReadyCondition) {
            await me.rowReadyCondition(config, {
              fromIndex,
              toIndex,
              fakeRows: fakeRows.slice(0, j)
            });
          }
          fakeRows.slice(0, j).forEach((fakeRow) => me.collectRow(fakeRow));
        }
        await me.onRowsCollected(rows, config);
      }
      totalHeight += top;
      if (hasMergeCells) {
        me.renderMergedCells(config, fromIndex, toIndex, rows);
      }
    }
    me.exportMeta.exactGridHeight = totalHeight;
    return totalHeight;
  }
  buildPageHtml(config) {
    const me = this, { subGrids } = me.exportMeta;
    let html = me.prepareExportElement();
    Object.values(subGrids).forEach(({ placeHolder, rows, mergedCellsHtml }) => {
      const placeHolderText = placeHolder.outerHTML;
      let contentHtml = rows.join("");
      if (mergedCellsHtml == null ? void 0 : mergedCellsHtml.length) {
        contentHtml += `<div class="b-grid-merged-cells-container">${mergedCellsHtml.join("")}</div>`;
      }
      html = html.replace(placeHolderText, contentHtml);
    });
    return html;
  }
};
__publicField(SinglePageExporter, "$name", "SinglePageExporter");
__publicField(SinglePageExporter, "type", "singlepage");
SinglePageExporter.prototype.pagesExtractor = async function* pagesExtractor4(config) {
  const me = this, { client } = config, { totalWidth } = me.exportMeta, styles2 = me.stylesheets, portrait = config.orientation === Orientation.portrait, paperFormat = PaperFormat[config.paperFormat], paperWidth = me.getPaperWidth(paperFormat, portrait), paperHeight = me.getPaperHeight(paperFormat, portrait), realPaperWidth = me.getPaperWidth(paperFormat, portrait), realPaperHeight = me.getPaperHeight(paperFormat, portrait), paperSize = config.lockPaperSize && isPaperSizeSupported(config.paperFormat) ? `@page { size: ${config.paperFormat} ${config.orientation} }` : "";
  let totalHeight, header, footer;
  totalHeight = await me.renderRows(config) + client.headerHeight + client.footerHeight;
  const html = me.buildPageHtml(config);
  const totalClientHeight = totalHeight;
  if (config.headerTpl) {
    header = me.prepareHTML(config.headerTpl({ totalWidth }));
    const height = me.measureElement(header, "b-export-header");
    totalHeight += height;
  }
  if (config.footerTpl) {
    footer = me.prepareHTML(config.footerTpl({ totalWidth }));
    const height = me.measureElement(footer, "b-export-footer");
    totalHeight += height;
  }
  const widthScale = Math.min(1, me.getScaleValue(me.inchToPx(paperWidth), totalWidth)), heightScale = Math.min(1, me.getScaleValue(me.inchToPx(paperHeight), totalHeight)), scale = Math.min(widthScale, heightScale);
  styles2.push(
    `<style>
            ${paperSize}
            #${client.id} {
                height: ${totalClientHeight}px !important;
                width: ${totalWidth}px !important;
            }
            .b-page-wrap {
                width: ${realPaperWidth}in;
                height: ${realPaperHeight}in;
            }
            .b-print .b-export-content {
                height: 100%;
            }
            ${me.centerContentHorizontally ? ".b-export-viewport > .b-widget { transform: translateX(-50%); left: 50%; }" : ""}
            .b-print .b-export-viewport {
                scale: ${scale};
                transform-origin: top left;
                height: calc(100% / ${scale});
                width: calc(100% / ${scale});
            }
        </style>`
  );
  yield {
    html: me.pageTpl({
      html,
      header,
      footer,
      styles: styles2,
      paperWidth,
      paperHeight,
      realPaperHeight,
      realPaperWidth,
      currentPage: 0
    })
  };
};
SinglePageExporter._$name = "SinglePageExporter";

// ../Grid/lib/Grid/feature/export/PdfExport.js
var PdfExport = class extends InstancePlugin {
  doDestroy() {
    var _a5;
    (_a5 = this.exportDialog) == null ? void 0 : _a5.destroy();
    this.exportersMap.forEach((exporter) => exporter.destroy());
    super.doDestroy();
  }
  /**
   * When export is started from GUI ({@link Grid.view.export.ExportDialog}), export promise can be accessed via
   * this property.
   * @property {Promise|null}
   */
  get currentExportPromise() {
    return this._currentExportPromise;
  }
  set currentExportPromise(value2) {
    this._currentExportPromise = value2;
  }
  get exportersMap() {
    return this._exportersMap || (this._exportersMap = /* @__PURE__ */ new Map());
  }
  getExporter(config = {}) {
    const me = this, { exportersMap } = me, { type } = config;
    let exporter;
    if (exportersMap.has(type)) {
      exporter = exportersMap.get(type);
      Object.assign(exporter, config);
    } else {
      const exporterClass = this.exporters.find((cls) => cls.type === type);
      if (!exporterClass) {
        throw new Error(`Exporter type ${type} is not found. Make sure you've configured it`);
      }
      config = ObjectHelper.clone(config);
      delete config.type;
      exporter = new exporterClass(config);
      exporter.relayAll(me);
      exportersMap.set(type, exporter);
    }
    return exporter;
  }
  buildExportConfig(config = {}) {
    const me = this, exportConfigs = [
      "client",
      "exportServer",
      "clientURL",
      "fileFormat",
      "fileName",
      "paperFormat",
      "rowsRange",
      "alignRows",
      "repeatHeader",
      "keepRegionSizes",
      "orientation",
      "translateURLsToAbsolute",
      "keepPathName",
      "sendAsBinary",
      "headerTpl",
      "footerTpl",
      "filterStyles",
      "useRenderedColumnWidth",
      "useBrowserPrint",
      "lockPaperSize",
      "rowReadyCondition"
    ], {
      client,
      filterStyles,
      fileName,
      rowReadyCondition
    } = me, result = {};
    if (!config.columns) {
      config.columns = client.columns.visibleColumns.filter((column) => column.exportable).map((column) => column.id);
    }
    exportConfigs.forEach((key) => result[key] = me[key]);
    ObjectHelper.assign(result, {
      exporterType: me.exporterType,
      fileName: fileName || client.$$name
    }, config);
    result.columns = config.columns.slice();
    if (!(result.exporterType === "multipagevertical" || result.exporterType === "verticalmultipagevertical")) {
      result.repeatHeader = false;
    }
    if (!("alignRows" in config) && config.repeatHeader) {
      result.alignRows = true;
    }
    result.exporterConfig = ObjectHelper.assign({
      type: result.exporterType,
      translateURLsToAbsolute: result.translateURLsToAbsolute,
      keepPathName: result.keepPathName,
      filterStyles,
      rowReadyCondition
    }, result.exporterConfig || {});
    delete result.exporterType;
    delete result.translateURLsToAbsolute;
    delete result.keepPathName;
    return result;
  }
  /**
   * Starts the export process. Accepts a config object which overrides any default configs.
   * **NOTE**. Component should not be interacted with when export is in progress
   *
   * @param {Object} config
   * @returns {Promise} Object of the following structure
   * ```
   * {
   *     response // Response instance
   * }
   * ```
   */
  async export(config = {}) {
    var _a5, _b2;
    const me = this, {
      client,
      pagesPerRequest
    } = me;
    config = me.buildExportConfig(config);
    let result;
    if (client.trigger("beforePdfExport", { config }) !== false) {
      client.isExporting = true;
      client.mask(me.exportMask);
      try {
        const exporter = me.getExporter(config.exporterConfig);
        if (pagesPerRequest === 0) {
          const pages = await exporter.export(config);
          if (me.isDestroying) {
            return;
          }
          (_a5 = me.exportDialog) == null ? void 0 : _a5.close();
          client.unmask();
          me.trigger("exportStep", { progress: 90, text: me.exportProgressMask, contentGenerated: true });
          if (config.useBrowserPrint) {
            await me.doPrint(pages, config);
            result = {};
          } else {
            const responsePromise = me.receiveExportContent(pages, config);
            me.toast = me.showLoadingToast(responsePromise);
            const response = await responsePromise;
            result = { response };
            await me.processExportContent(response, config);
          }
        }
      } catch (error) {
        if (error instanceof Response) {
          result = { response: error };
        } else {
          result = { error };
        }
        throw error;
      } finally {
        if (me.toast && !me.toast.isDestroying) {
          me.toast.hide();
        }
        if (!me.isDestroying) {
          (_b2 = me.exportDialog) == null ? void 0 : _b2.close();
          client.unmask();
          if (me.showErrorToast) {
            if (result.error) {
              if (result.error.name !== "AbortError") {
                Toast.show({
                  html: me.L("L{Export failed}"),
                  rootElement: me.rootElement
                });
              }
            } else if (result.response && !result.response.ok) {
              Toast.show({
                html: me.L("L{Server error}"),
                rootElement: me.rootElement
              });
            }
          }
          client.trigger("pdfExport", result);
          client.isExporting = false;
        }
      }
    }
    return result;
  }
  /**
   * Sends request to the export server and returns Response instance. This promise can be cancelled by the user
   * by clicking on the toast message. When the user clicks on the toast, `abort` method is called on the promise
   * returned by this method. If you override this method you can implement `abort` method like in the snippet
   * below to cancel the request.
   *
   * ```javascript
   * class MyPdfExport extends PdfExport {
   *     receiveExportContent(pages, config) {
   *         let controller;
   *
   *         const promise = new Promise(resolve => {
   *             controller = new AbortController();
   *             const signal = controller.signal;
   *
   *             fetch(url, { signal })
   *                 .then(response => resolve(response));
   *         });
   *
   *         // This method will be called when user clicks on the toast message to cancel the request
   *         promise.abort = () => controller.abort();
   *
   *         return promise;
   *     }
   * }
   *
   * const grid = new Grid({ features: { myPdfExport : {...} } });
   *
   * grid.features.myPdfExport.export().catch(e => {
   *     // In case of aborted request do nothing
   *     if (e.name !== 'AbortError') {
   *         // handle other exceptions
   *     }
   * });
   * ```
   * @param {Object[]} pages Array of exported pages. Each page has `html` property, which includes HTML
   *   of the exported page.
   * @param {Object} config Export config
   * @param {String} config.exportServer URL of the export server.
   * @param {String} config.orientation Page orientation. portrait/landscape.
   * @param {String} config.paperFormat Paper format as supported by puppeteer. A4/A3/...
   * @param {String} config.fileFormat File format. PDF/PNG.
   * @param {String} config.fileName Name to use for the saved file.
   * @param {String} config.clientURL URL to navigate before export. See {@link #config-clientURL}.
   * @param {String} config.sendAsBinary Tells server whether to return binary file instead of download link.
   * @returns {Promise} Returns Response instance
   */
  receiveExportContent(pages, config) {
    return AjaxHelper.fetch(
      config.exportServer,
      Object.assign({
        method: "POST",
        credentials: "omit",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          html: pages,
          orientation: config.orientation,
          format: config.paperFormat,
          fileFormat: config.fileFormat,
          fileName: config.fileName,
          clientURL: config.clientURL,
          sendAsBinary: config.sendAsBinary
        })
      }, this.fetchOptions)
    );
  }
  /**
   * Handles output of the {@link #function-receiveExportContent}. Server response can be of two different types depending
   * on {@link #config-sendAsBinary} config:
   * - `application/json` In this case JSON response contains url of the file to download
   * - `application/octet-stream` In this case response contains stream of file binary data
   *
   * If {@link #config-openAfterExport} is true, this method will try to download content.
   * @param {Response} response
   * @param {Object} config Export config
   * @param {String} config.exportServer URL of the export server.
   * @param {String} config.orientation Page orientation. portrait/landscape.
   * @param {String} config.paperFormat Paper format as supported by puppeteer. A4/A3/...
   * @param {String} config.fileFormat File format. PDF/PNG.
   * @param {String} config.fileName Name to use for the saved file.
   * @param {String} config.clientURL URL to navigate before export. See {@link #config-clientURL}.
   * @param {String} config.sendAsBinary Tells server whether to return binary file instead of download link. See {@link #config-sendAsBinary}
   */
  async processExportContent(response, config) {
    const me = this;
    if (response.ok && me.openAfterExport) {
      response = response.clone();
      const contentType = response.headers.get("content-type");
      if (contentType.match(/application\/octet-stream/)) {
        const MIMEType = FileMIMEType[config.fileFormat], objectURL = await me.responseBlobToObjectURL(response, MIMEType), link = me.getDownloadLink(config.fileName, objectURL);
        link.click();
      } else if (contentType.match(/application\/json/)) {
        const responseJSON = await response.json();
        if (responseJSON.success) {
          const link = me.getDownloadLink(config.fileName, responseJSON.url);
          link.click();
        } else {
          Toast.show({
            html: responseJSON.msg,
            rootElement: this.rootElement
          });
        }
      }
    }
  }
  doPrint(pages) {
  }
  /**
   * Creates object URL from response content with given mimeType
   * @param {Response} response Response instance
   * @param {String} mimeType
   * @returns {Promise} Returns string object URL
   * @private
   */
  async responseBlobToObjectURL(response, mimeType) {
    const blob = await response.blob();
    return URL.createObjectURL(blob.slice(0, blob.size, mimeType));
  }
  /**
   * Creates link to download the file.
   * @param {String} name File name
   * @param {String} href URL of the resource
   * @returns {HTMLElement} HTMLAnchorElement
   * @private
   */
  getDownloadLink(name, href) {
    const link = document.createElement("a");
    link.download = name;
    link.href = href;
    if (this.openInNewTab) {
      link.target = "_blank";
    }
    return link;
  }
  get defaultExportDialogConfig() {
    const me = this;
    return {
      client: me.client,
      exporters: me.exporters,
      useBrowserPrint: me.useBrowserPrint,
      items: {
        rowsRangeField: {
          value: me.rowsRange
        },
        exporterTypeField: {
          value: me.exporterType
        },
        orientationField: {
          value: me.orientation
        },
        paperFormatField: {
          value: me.paperFormat
        },
        repeatHeaderField: {
          value: me.repeatHeader
        },
        fileFormatField: {
          value: me.fileFormat
        },
        alignRowsField: {
          checked: me.alignRows
        }
      }
    };
  }
  changeExportDialog(exportDialog, oldExportDialog) {
    const me = this;
    oldExportDialog == null ? void 0 : oldExportDialog.destroy();
    if (exportDialog) {
      const config = me.dialogClass.mergeConfigs({
        rootElement: me.rootElement,
        client: me.client,
        modal: {
          transparent: true
        },
        items: {
          rowsRangeField: {
            value: me.rowsRange
          },
          exporterTypeField: {
            value: me.exporterType
          },
          orientationField: {
            value: me.orientation
          },
          paperFormatField: {
            value: me.paperFormat
          },
          repeatHeaderField: {
            value: me.repeatHeader
          },
          fileFormatField: {
            value: me.fileFormat
          },
          alignRowsField: {
            checked: me.alignRows
          }
        }
      }, me.defaultExportDialogConfig, exportDialog);
      exportDialog = me.dialogClass.new(config);
      exportDialog.ion({
        export: me.onExportButtonClick,
        thisObj: me
      });
    }
    return exportDialog;
  }
  /**
   * Shows {@link Grid.view.export.ExportDialog export dialog}
   */
  async showExportDialog() {
    return this.exportDialog.show();
  }
  /**
   * This yields `true` if an export/print operation is ongoing.
   * @property {Boolean}
   * @readonly
   */
  get isExporting() {
    return Boolean(this.client.isExporting);
  }
  onExportButtonClick({ values }) {
    const me = this, dialogMask = me.exportDialog.mask({
      progress: 0,
      maxProgress: 100,
      text: me.exportMask
    });
    const detacher2 = me.ion({
      exportstep({ progress, text, contentGenerated }) {
        if (contentGenerated) {
          me.exportDialog.unmask();
          detacher2();
        } else {
          dialogMask.progress = progress;
          if (text != null) {
            dialogMask.text = text;
          }
        }
      }
    });
    me.currentExportPromise = me.export(values);
    me.currentExportPromise.catch((e) => console.error(e)).finally(() => {
      var _a5;
      detacher2();
      (_a5 = me.exportDialog) == null ? void 0 : _a5.unmask();
      me.currentExportPromise = null;
    });
  }
  showLoadingToast(exportPromise) {
    const toast = Toast.show({
      timeout: 0,
      showProgress: false,
      rootElement: this.rootElement,
      html: `
    <span class="b-mask-icon b-icon b-icon-spinner"></span>
    <span>${this.exportProgressMask}</span>
    <button class="b-button">${this.L("L{Click to abort}")}</button>`
    });
    EventHelper.on({
      element: toast.element,
      click() {
        var _a5;
        (_a5 = exportPromise.abort) == null ? void 0 : _a5.call(exportPromise);
      }
    });
    return toast;
  }
};
__publicField(PdfExport, "$name", "PdfExport");
__publicField(PdfExport, "configurable", {
  dialogClass: ExportDialog,
  /**
   * URL of the print server.
   * @config {String}
   */
  exportServer: void 0,
  /**
   * Returns the instantiated export dialog widget as configured by {@link #config-exportDialog}
   * @member {Grid.view.export.ExportDialog} exportDialog
   */
  /**
   * A config object to apply to the {@link Grid.view.export.ExportDialog} widget.
   * @config {ExportDialogConfig}
   */
  exportDialog: {
    value: true,
    $config: ["lazy"]
  },
  /**
   * Name of the exported file.
   * @config {String}
   * @category Export file config
   */
  fileName: null,
  /**
   * Format of the exported file, either `pdf` or `png`.
   * @config {'pdf'|'png'}
   * @default
   * @category Export file config
   */
  fileFormat: "pdf",
  /**
   * Export server will navigate to this url first and then will change page content to whatever client sent.
   * This option is useful with react dev server, which uses a strict CORS policy.
   * @config {String}
   */
  clientURL: null,
  /**
   * Export paper format. Available options are A1...A5, Legal, Letter.
   * @config {'A0'|'A1'|'A2'|'A3'|'A4'|'A5'|'Legal'|'Letter'}
   * @default
   * @category Export file config
   */
  paperFormat: "A4",
  /**
   * Orientation. Options are `portrait` and `landscape`.
   * @config {'portrait'|'landscape'}
   * @default
   * @category Export file config
   */
  orientation: "portrait",
  /**
   * Specifies which rows to export. `all` for complete set of rows, `visible` for only rows currently visible.
   * @config {'all'|'visible'}
   * @category Export file config
   * @default
   */
  rowsRange: "all",
  /**
   * Set to `true` to align row top to the page top on every exported page. Only applied to multipage export.
   * @config {Boolean}
   * @default
   */
  alignRows: false,
  /**
   * Set to `true` to show column headers on every page. This will also set {@link #config-alignRows} to true.
   * Only applies to MultiPageVertical exporter.
   * @config {Boolean}
   * @default
   */
  repeatHeader: false,
  /**
   * By default, subGrid width is changed to fit all exported columns. To keep certain subGrid size specify it
   * in the following form:
   * ```javascript
   * keepRegionSizes : {
   *     locked : true
   * }
   * ```
   * @config {Object<String,Boolean>}
   * @default
   */
  keepRegionSizes: null,
  /**
   * When exporting large views (hundreds of pages) stringified HTML may exceed browser or server request
   * length limit. This config allows to specify how many pages to send to server in one request.
   * @config {Number}
   * @default
   * @private
   */
  pagesPerRequest: 0,
  /**
   * Config for exporter.
   * @config {Object}
   * @private
   */
  exporterConfig: null,
  /**
   * Type of the exporter to use. Should be one of the configured {@link #config-exporters}
   * @config {'singlepage'|'multipage'|'multipagevertical'|String}
   * @default
   */
  exporterType: "singlepage",
  /**
   * List of exporter classes to use in export feature
   * @config {Grid.feature.export.exporter.Exporter[]}
   * @default
   */
  exporters: [SinglePageExporter, MultiPageExporter, MultiPageVerticalExporter],
  /**
   * `True` to replace all linked CSS files URLs to absolute before passing HTML to the server.
   * When passing a string the current origin of the CSS files URLS will be replaced by the passed origin.
   *
   * For example: css files pointing to /app.css will be translated from current origin to {translateURLsToAbsolute}/app.css
   * @config {Boolean|String}
   * @default
   */
  translateURLsToAbsolute: true,
  /**
   * When true links are converted to absolute by combining current window location (with replaced origin) with
   * resource link.
   * When false links are converted by combining new origin with resource link (for angular)
   * @config {Boolean}
   * @default
   */
  keepPathName: true,
  /**
   * When true, page will attempt to download generated file.
   * @config {Boolean}
   * @default
   */
  openAfterExport: true,
  /**
   * Set to `true` to receive binary file from the server instead of download link.
   * @config {Boolean}
   * @default
   */
  sendAsBinary: false,
  /**
   * False to open in the current tab, true - in a new tab
   * @config {Boolean}
   * @default
   */
  openInNewTab: false,
  /**
   * A template function used to generate a page header. It is passed an object with ´currentPage´ and `totalPages´ properties.
   *
   * ```javascript
   * let grid = new Grid({
   *     appendTo   : 'container',
   *     features : {
   *         pdfExport : {
   *             exportServer : 'http://localhost:8080/',
   *             headerTpl : ({ currentPage, totalPages }) => `
   *                 <div class="demo-export-header">
   *                     <img src="coolcorp-logo.png"/>
   *                     <dl>
   *                         <dt>Date: ${DateHelper.format(new Date(), 'll LT')}</dt>
   *                         <dd>${totalPages ? `Page: ${currentPage + 1}/${totalPages}` : ''}</dd>
   *                     </dl>
   *                 </div>`
   *          }
   *     }
   * });
   * ```
   * @config {Function}
   * @param {Object} data Data object
   * @param {Number} data.currentPage Current page number
   * @param {Number} data.totalPages Tolal pages count
   * @returns {String}
   */
  headerTpl: null,
  /**
   * A template function used to generate a page footer. It is passed an object with ´currentPage´ and `totalPages´ properties.
   *
   * ```javascript
   * let grid = new Grid({
   *      appendTo   : 'container',
   *      features : {
   *          pdfExport : {
   *              exportServer : 'http://localhost:8080/',
   *              footerTpl    : () => '<div class="demo-export-footer"><h3>© 2020 CoolCorp Inc</h3></div>'
   *          }
   *      }
   * });
   * ```
   *
   * @config {Function}
   * @param {Object} data Data object
   * @param {Number} data.currentPage Current page number
   * @param {Number} data.totalPages Tolal pages count
   * @returns {String}
   */
  footerTpl: null,
  /**
   * An object containing the Fetch options to pass to the export server request. Use this to control if
   * credentials are sent and other options, read more at
   * [MDN](https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch).
   * @config {FetchOptions}
   */
  fetchOptions: null,
  /**
   * A message to be shown when Export feature is performing export.
   * @config {String}
   * @default "Generating pages..."
   */
  exportMask: "L{Generating pages}",
  /**
   * A message to be shown when export is almost done.
   * @config {String}
   * @default "Waiting for response from server..."
   */
  exportProgressMask: "L{Waiting for response from server}",
  /**
   * Set to `false` to not show Toast message on export error.
   * @config {Boolean}
   * @default
   */
  showErrorToast: true,
  localizableProperties: ["exportMask", "exportProgressMask"],
  /**
   * This method accepts all stylesheets (link and style tags) which are supposed to be put on the page. Use
   * this hook method to filter or modify them.
   *
   * ```javascript
   * new Grid({
   *     features: {
   *         pdfExport: {
   *             // filter out inline styles and bootstrap.css
   *             filterStyles: styles => styles.filter(item => !/(link|bootstrap.css)/.test(item))
   *         }
   *     }
   * });
   * ```
   * @param {String[]} styles
   * @returns {String[]} List of stylesheets to put on the exported page
   */
  filterStyles: (styles2) => styles2,
  /**
   * A function that determines when to capture the HTML for exported rows. When set to null (the default),
   * the export happens immediately without waiting. The function receives the export configuration as a
   * parameter.
   * The rendering process works in chunks, using the same approach as the Grid component - displaying visible
   * rows with an additional buffer.
   *
   * Examples:
   * ```javascript
   * // simple timeout
   * new Grid({
   *     features : {
   *         pdfExport : {
   *             rowReadyCondition : async () => await new Promise(resolve => setTimeout(resolve, 100))
   *         }
   *     }
   * });
   *
   * // waiting for a condition
   * new Grid({
   *     features : {
   *         pdfExport : {
   *             rowReadyCondition : async () => await promiseWithCondition
   *         }
   *     }
   * });
   * ```
   * @prp {Function}
   * @returns {void}
   */
  rowReadyCondition: null,
  /**
   * This config forces exporter to always use rendered column width. Used by Agenda view in Calendar
   * @private
   */
  useRenderedColumnWidth: false,
  /**
   * When true, export feature will use an iframe and browser's default print dialog, which allows to save
   * as PDF. Content is optimized for chrome/edge to exact page size in the specified orientation and no
   * margins. If you only see grid header or blank pages, try using different scale value in the print dialog
   * **NOTE**: Not supported in Safari. Print works, but Safari cannot seem to fit content to one page
   * correctly.
   * @config {Boolean}
   * @default
   * @private
   */
  useBrowserPrint: false,
  /**
   * Defines if printed/exported page should contain `@page` style with paper size and orientation specified.
   * @config {Boolean}
   * @default
   */
  lockPaperSize: false,
  testConfig: {
    // Cant open export in tests
    openAfterExport: false
  }
});
PdfExport._$name = "PdfExport";
GridFeatureManager.registerFeature(PdfExport, false, "Grid");

// ../Grid/lib/Grid/feature/export/mixin/PrintMixin.js
var PrintMixin_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    static get configurable() {
      return {
        useBrowserPrint: true,
        exporterType: "multipagevertical",
        // This is a private config to make print only render content to an iframe for testing purposes
        test: false
      };
    }
    static get pluginConfig() {
      return {
        assign: ["print", "showPrintDialog"]
      };
    }
    /**
     * Shows the {@link Grid.view.export.ExportDialog print dialog}
     * @returns {Promise}
     * @on-owner
     * @category Print
     */
    showPrintDialog() {
      return this.showExportDialog();
    }
    /**
     * Starts the print process. Accepts a config object which overrides any default configs.
     * **NOTE** Component should not be interacted with when print is in progress
     *
     * @param {Object} config
     * @returns {Promise} Promise which resolves when printing is done. Optionally it might return an object with an
     * `error` key in it.
     * @on-owner
     * @category Print
     */
    print(config) {
      return this.export(config);
    }
    /**
     * This method is called when IFrame is loaded with all the HTML/CSS and is about to be printed. Use it to take
     * control over the page contents.
     * @param {HTMLIFrameElement} iframe
     */
    async onPrintIFrameLoad(iframe) {
    }
    async showBrowserPrintDialog(iframe, resolve) {
      await this.onPrintIFrameLoad(iframe);
      if (await this.client.trigger("beforeShowPrintDialog", { iframe }) !== false) {
        if (!this.test) {
          const { contentWindow } = iframe;
          contentWindow.onafterprint = () => {
            iframe.remove();
            resolve();
          };
          contentWindow.print();
        }
      } else {
        iframe.remove();
        resolve();
      }
    }
    addStylesToDocument(doc2, fragment, html) {
      fragment.head.querySelectorAll('link[rel="stylesheet"],link[as="style"]').forEach((styleEl) => {
        const el = doc2.adoptNode(styleEl);
        doc2.head.appendChild(el);
      });
    }
    addInlineStylesToDocument(doc2, fragment) {
      fragment.head.querySelectorAll("style").forEach((styleEl) => {
        const el = doc2.adoptNode(styleEl);
        doc2.head.appendChild(el);
      });
    }
    doPrint(pages) {
      const me = this;
      return new Promise((resolve, reject) => {
        const iframe = document.createElement("iframe");
        iframe.className = "b-print-wrapper";
        iframe.style.visibility = "hidden";
        iframe.style.height = "0";
        iframe.onload = () => {
          var _a6;
          const handle = iframe.contentWindow, doc2 = handle.document, { body } = doc2, parser = new DOMParser();
          let paperHeight, paperWidth;
          pages.forEach(({ html: html2 }, index) => {
            const fragment = parser.parseFromString(html2, "text/html"), node = doc2.adoptNode(fragment.body.firstChild);
            if (index === 0) {
              me.addStylesToDocument(doc2, fragment, html2);
              paperHeight = parseFloat(fragment.body.style.height);
              paperWidth = parseFloat(fragment.body.style.width);
            }
            me.addInlineStylesToDocument(doc2, fragment);
            body.appendChild(node);
          });
          const { html } = pages[0];
          body.className = ((_a6 = html.match(/<body class="(.+?)"/)) == null ? void 0 : _a6[1]) || "";
          body.parentElement.classList.add("b-print-root");
          body.style.height = `${paperHeight * pages.length}in`;
          body.style.width = `${paperWidth}in`;
          Promise.all(Array.from(doc2.head.querySelectorAll('link[rel="stylesheet"]')).map((link) => {
            return new Promise((resolve2, reject2) => {
              link.onload = resolve2;
              link.onerror = reject2;
            });
          })).then(() => doc2.fonts.ready).then(() => me.showBrowserPrintDialog(iframe, resolve)).catch((e) => {
            console.warn(`Failed to load stylesheets ${e.message ? `: ${e.message}` : ""}`);
            reject(me.L("L{PdfExport.Export failed}"));
          });
        };
        me.client.element.parentElement.appendChild(iframe);
      });
    }
  }, __publicField(_a5, "$name", "PrintMixin"), _a5;
};

// ../Grid/lib/Grid/feature/export/Print.js
var Print = class extends PrintMixin_default(PdfExport) {
};
/**
 * @hideConfigs clientURL, exportServer, fetchOptions, fileFormat, fileName, openAfterExport, openInNewTab, sendAsBinary
 */
/**
 * @hideFunctions processExportContent, receiveExportContent, showExportDialog
 */
__publicField(Print, "$name", "Print");
Print._$name = "Print";
GridFeatureManager.registerFeature(Print, false, "Grid");

// ../Grid/lib/Grid/util/TableExporter.js
var TableExporter = class extends Base {
  static get defaultConfig() {
    return {
      /**
       * Target grid instance to export data from
       * @config {Grid.view.Grid} target
       */
      target: null,
      /**
       * Specifies a default column width if no width specified
       * @config {Number} defaultColumnWidth
       * @default
       */
      defaultColumnWidth: 100,
      /**
       * Set to `false` to export dates as they are displayed by Date column formatter
       * @config {Boolean}
       * @default
       */
      exportDateAsInstance: true,
      /**
       * If true and the grid is grouped, shows the grouped value in the first column. True by default.
       * @config {Boolean} showGroupHeader
       * @default
       */
      showGroupHeader: true,
      /**
       * An array of column configuration objects used to specify column widths, header text, and data fields to get the data from.
       * 'field' config is required. If 'text' is missing, it will read it from the grid column or the 'field' config.
       * If 'width' is missing, it will try to get it retrieved from the grid column or {@link #config-defaultColumnWidth} config.
       * If no columns provided the config will be generated from the grid columns.
       *
       * For example:
       * ```javascript
       * columns : [
       *     'firstName', // field
       *     'age', // field
       *     { text : 'Starts', field : 'start', width : 140 },
       *     { text : 'Ends', field : 'finish', width : 140 }
       * ]
       * ```
       *
       * @config {String[]|Object[]}
       */
      columns: null,
      /**
       * When true and tree is being exported, node names are indented with {@link #config-indentationSymbol}
       * @config {Boolean}
       * @default
       */
      indent: true,
      /**
       * This symbol (four spaces by default) is used to indent node names when {@link #config-indent} is true
       * @config {String}
       * @default
       */
      indentationSymbol: "\xA0\xA0\xA0\xA0"
    };
  }
  /**
   * Exports grid data according to provided config
   * @param {Object} config
   * @returns {{ rows : Object[][], columns : Object[] }}
   */
  export(config = {}) {
    const me = this;
    config = ObjectHelper.assign({}, me.config, config);
    me.normalizeColumns(config);
    return me.generateExportData(config);
  }
  generateExportData(config) {
    const me = this, columns = me.generateColumns(config), rows = me.generateRows(config);
    return { rows, columns };
  }
  normalizeColumns(config) {
    var _a5, _b2;
    const columns = config.columns || (config.exportAllColumns ? (_a5 = this.target.columns) == null ? void 0 : _a5.records : (_b2 = this.target.columns) == null ? void 0 : _b2.visibleColumns).filter((rec) => rec.exportable !== false);
    config.columns = columns.map((col) => {
      if (typeof col === "string") {
        return this.target.columns.find((column) => column.field === col) || { field: col };
      } else {
        return col;
      }
    });
  }
  generateColumns(config) {
    return config.columns.map((col) => this.processColumn(col, config));
  }
  generateRows(config) {
    var _a5;
    const { columns, rows } = config;
    if (columns.length === 0 || (rows == null ? void 0 : rows.length) === 0) {
      return [];
    }
    const me = this, { target } = me;
    const exportedRows = (rows || target.store).map((record) => me.processRecord(record, columns, config)).filter((cells) => cells == null ? void 0 : cells.length);
    if ((_a5 = target.features.summary) == null ? void 0 : _a5.enabled) {
      const div = document.createElement("div"), summaryRow = columns.map((col) => {
        div.innerHTML = target.features.summary.generateHtml(col, rows || target.store.records);
        return div.innerText;
      });
      exportedRows.push(summaryRow);
    }
    return exportedRows;
  }
  getColumnType(column, store = this.target.store) {
    let result = column.exportedType || "object";
    if (column.exportedType === void 0) {
      if (column.field) {
        const fieldDefinition = store.modelClass.getFieldDefinition(column.field);
        if (fieldDefinition && fieldDefinition.type !== "auto") {
          result = fieldDefinition.type;
        }
      }
    }
    return result;
  }
  /**
   * Extracts export data from the column instance
   * @param {Grid.column.Column} column
   * @param {Object} config
   * @private
   * @returns {Object}
   */
  processColumn(column, config) {
    const me = this, { target } = me, { defaultColumnWidth } = config;
    let { field, text: value2, width, minWidth } = column;
    if (target.store && !(field in target.store.modelClass.fieldMap)) {
      field = "";
    }
    if (!value2 || !width) {
      const gridColumn = target.columns.find((col) => col.field === field);
      if (!value2) {
        value2 = gridColumn && (gridColumn.text || gridColumn.headerRenderer && gridColumn.textElement.innerText) || field;
      }
      if (width == null) {
        width = gridColumn && gridColumn.width || defaultColumnWidth;
      }
    }
    width = Math.max(width || defaultColumnWidth, minWidth || defaultColumnWidth);
    return { field, value: value2, width, type: me.getColumnType(column) };
  }
  /**
   * Extracts export data from the record instance reading supplied column configs
   * @param {Core.data.Model|null} record If null is passed, all columns will be filled with empty strings
   * @param {Grid.column.Column[]} columns
   * @param {Object} config
   * @private
   * @returns {Object[]}
   */
  processRecord(record, columns, config) {
    const { target } = this, {
      showGroupHeader,
      indent,
      indentationSymbol
    } = config;
    let cells;
    if (!record) {
      cells = columns.map(() => "");
    } else if (record.isSpecialRow) {
      if (showGroupHeader && "groupRowFor" in record.meta) {
        cells = columns.map((column) => {
          return target.features.group.buildGroupHeader({
            // Create dummy element to get html from
            cellElement: DomHelper.createElement(),
            grid: target,
            isExporting: true,
            record,
            column,
            isExport: true
          });
        });
      }
      if (record.meta.groupFooterFor && !target.isTimelineBase) {
        const div = document.createElement("div"), children = record.groupChildren.slice();
        children.pop();
        cells = columns.map((col) => {
          div.innerHTML = target.features.groupSummary.generateHtml(col, children, record, target.features.group.field, record._groupValue);
          return div.innerText;
        });
      }
    } else {
      cells = columns.map((column) => {
        var _a5;
        let value2 = record.getValue(column.field);
        const activeRenderer = column.renderer;
        if (activeRenderer && !(value2 && column.isDateColumn && config.exportDateAsInstance)) {
          value2 = (_a5 = activeRenderer.call(column, {
            value: value2,
            record,
            column,
            grid: target,
            isExport: true
          })) != null ? _a5 : value2;
        }
        if (indent && column.tree) {
          value2 = `${indentationSymbol.repeat(record.childLevel)}${value2}`;
        }
        if (column.afterRenderCell) {
          value2 = column.callback(column.afterRenderCell, column, [{
            value: value2,
            record,
            column,
            grid: target,
            isExport: true
          }]);
        }
        return value2;
      });
    }
    return cells;
  }
};
TableExporter._$name = "TableExporter";

// ../Grid/lib/Grid/feature/experimental/xlsproviders/XlsProviderBase.js
var XlsProviderBase = class {
  /**
   * Implement this method to write data to an xls file
   * @param {Object} config
   * @param {String} config.filename Name of the exported file
   * @param {XLSCell[][]} config.rows Array of rows, each row is an array of cells
   * @param {XLSColumn[]} config.columns Array of columns
   * @param {Object} config.sheet *deprecated, to be removed in 7.0* Object with columns and data
   * @param {XLSCell[][]} config.sheet.data Array of rows, each row is an array of cells
   * @param {XLSColumn[]} config.sheet.cols Array of columns
   * @param {String} config.MIME File MIME type
   * @returns {void}
   */
  static write(config) {
    throw new Error("XlsProviderBase#write must be implemented in a subclass");
  }
};
XlsProviderBase._$name = "XlsProviderBase";

// ../Grid/lib/Grid/feature/experimental/xlsproviders/WriteExcelFileProvider.js
var typeMap = {
  string: String,
  number: Number,
  date: Date,
  boolean: Boolean,
  bool: Boolean
};
var WriteExcelFileProvider = class extends XlsProviderBase {
  /**
   * This function accepts data to put to Excel file and uses write-excel-file library to create xlsx file.
   * @param {Object} config
   * @param {String} config.filename Name of the exported file
   * @param {XLSCell[][]} config.rows Array of rows, each row is an array of cells
   * @param {XLSColumn[]} config.columns Array of columns
   * @param {Object} config.sheet *deprecated, to be removed in 7.0* Object with columns and data
   * @param {XLSCell[][]} config.sheet.data Array of rows, each row is an array of cells
   * @param {XLSColumn[]} config.sheet.cols Array of columns
   * @param {String} config.MIME File MIME type
   * @returns {void}
   */
  static write({ filename, columns, rows }) {
    rows.forEach((row) => {
      row.forEach((cell) => {
        cell.type = typeMap[cell.type] || String;
        if (cell.type === String && typeof cell.value !== "string") {
          cell.value = `${cell.value}`;
        }
      });
    });
    columns.map((cell) => {
      delete cell.type;
      delete cell.field;
      cell.fontWeight = "bold";
      cell.align = "center";
    });
    globalThis.writeXlsxFile(
      [columns, ...rows],
      {
        dateFormat: "yyyy-mm-dd",
        fileName: filename,
        columns: columns.map((cell) => {
          return {
            width: Math.round((cell.width || 100) / 10)
          };
        })
      }
    );
  }
};
WriteExcelFileProvider._$name = "WriteExcelFileProvider";

// ../Grid/lib/Grid/feature/experimental/ExcelExporter.js
var ExcelExporter = class extends InstancePlugin {
  processValue(value2) {
    if (value2 === void 0 || value2 === null || Number.isNaN(value2) || typeof value2 === "function" || typeof value2 === "object" && String(value2) === "[object Object]") {
      return "";
    }
    return value2;
  }
  generateExportData(config) {
    const me = this, { rows, columns } = me.exporter.export(config.exporterConfig);
    return {
      rows: rows.map((row) => {
        return row.map((value2, index) => {
          var _a5;
          let type = ((_a5 = columns[index]) == null ? void 0 : _a5.type) || "string";
          if (type === "object" || type === "auto") {
            type = "string";
          }
          if (value2 instanceof Date) {
            if (config.dateFormat) {
              value2 = DateHelper.format(value2, config.dateFormat);
              type = "string";
            } else {
              value2 = TimeZoneHelper.fromTimeZone(value2, 0);
              type = "date";
            }
          } else if (type === "number" && typeof value2 === "string") {
            value2 = parseFloat(value2.replace(/^\D*/, ""));
          }
          if (me.convertEmptyValueToEmptyString) {
            value2 = me.processValue(value2);
          } else if (type === "string" && typeof value2 !== "string") {
            value2 = `${value2}`;
          }
          return { value: value2, type };
        });
      }),
      columns
    };
  }
  /**
   * Generate and download an Excel (.xlsx), or CSV file (.csv).
   * @param {Object} config Optional configuration object, which overrides initial settings of the feature/exporter.
   * @param {String} [config.filename] Name of the exported file
   * @param {String} [config.dateFormat] Defines how dates in a cell will be formatted
   * @param {Boolean|Object} [config.csv] Set to `true` to output as a CSV file, or as an object where you can specify
   * delimiters.
   * @param {String} [config.csv.columnDelimiter] The CSV delimiter to separate values on one line, defaults to `,`.
   * @param {String} [config.csv.lineDelimiter] The CSV delimiter to separate lines, defaults to `\n`.
   * @param {String[]|Object[]} [config.columns] An array of column configuration objects
   * @param {Core.data.Model[]} [config.rows] An array of records to export
   * @returns {Promise} Promise that resolves when the export is completed
   */
  async export(config = {}) {
    var _a5;
    const me = this, { client } = me, exporter = ((_a5 = me.xlsProvider) == null ? void 0 : _a5.write) || me.zipcelx || globalThis.zipcelx;
    if (!exporter) {
      throw new Error('ExcelExporter: "xlsProvider" or "zipcelx" library is required');
    }
    if (me.disabled) {
      return;
    }
    config = ObjectHelper.assign({}, me.config, config);
    if (!config.filename) {
      config.filename = client.$$name;
    }
    const { filename } = config, { rows, columns } = me.generateExportData(config);
    if (config.csv) {
      const columnDelimiter = config.csv.columnDelimiter || ",", lineDelimiter = config.csv.lineDelimiter || "\n";
      if (client.trigger("beforeCSVExport", { config, rows, columns, lineDelimiter, columnDelimiter }) !== false) {
        const headers = columns.map((col) => this.processCsvValue(col.value)).join(columnDelimiter) + lineDelimiter, text = rows.map((rowValues) => rowValues.map((obj) => this.processCsvValue(obj.value)).join(`${columnDelimiter}`)).join(lineDelimiter), bom = new Uint8Array([239, 187, 191]), blob = new Blob([bom, headers + text], { type: me.csvMimeType });
        BrowserHelper.downloadBlob(blob, filename);
      }
      return Promise.resolve();
    }
    if (client.trigger("beforeExcelExport", { config, rows, columns }) !== false) {
      const params = {
        filename,
        columns,
        rows
      };
      if (VersionHelper.checkVersion("core", "7.0", "<")) {
        params.sheet = {};
        params.sheet.data = [...rows.map((row) => row.map((cell, index) => {
          var _a6;
          return {
            ...cell,
            type: ((_a6 = columns[index]) == null ? void 0 : _a6.type) === "number" && typeof value !== "string" ? "number" : "string"
          };
        }))];
        params.sheet.cols = columns.map((col) => ({
          ...col,
          type: "string"
        }));
        params.sheet.data.unshift(params.sheet.cols);
      }
      return exporter(params);
    }
    return Promise.resolve();
  }
  processCsvValue(text = "") {
    return `"${String(text).replace(/"/g, '""')}"`;
  }
  construct(grid, config) {
    super.construct(grid, config);
    if (!this.zipcelx) {
      if (typeof zipcelx !== "undefined") {
        this.zipcelx = globalThis.zipcelx;
      }
    }
  }
  get exporter() {
    const me = this;
    return me._exporter || (me._exporter = me.exporterClass.new({ target: me.client }, me.exporterConfig));
  }
};
__publicField(ExcelExporter, "$name", "ExcelExporter");
__publicField(ExcelExporter, "configurable", {
  /**
   * Name of the exported file
   * @config {String} filename
   * @default
   */
  filename: null,
  /**
   * Defines how dates in a cell will be formatted
   * @config {String} dateFormat
   * @default
   */
  dateFormat: VersionHelper.checkVersion("core", "7.0", ">=") ? null : "YYYY-MM-DD",
  /**
   * Exporter class to use as a data provider. {@link Grid.util.TableExporter} by default.
   * @config {Grid.util.TableExporter}
   * @typings {typeof TableExporter}
   * @default
   */
  exporterClass: TableExporter,
  /**
   * Configuration object for {@link #config-exporterClass exporter class}.
   * @config {Object}
   */
  exporterConfig: null,
  /**
   * This hook allows to use 3rd party libraries to generate XLSX files. Until version 7.0 zipcelx library is
   * used. From version 7.0, the default will be {@link Grid/feature/experimental/xlsproviders/WriteExcelFileProvider}.
   * Provider must extend {@link Grid/feature/experimental/xlsproviders/XlsProviderBase} and implement static
   * {@link Grid/feature/experimental/xlsproviders/XlsProviderBase#function-write-static write} method.
   *
   * @config {Class}
   * @typings {typeof XlsProviderBase}
   */
  xlsProvider: VersionHelper.checkVersion("core", "7.0", ">=") ? WriteExcelFileProvider : null,
  /**
   * Allows configuring MIME type of the exported CSV file
   * @config {String}
   * @default
   * @internal
   */
  csvMimeType: "text/csv",
  /**
   * Legacy way to generate XLS files was using [zipcelx library](https://www.npmjs.com/package/zipcelx)
   * forked and adjusted to support [column width config](https://github.com/bryntum/zipcelx/tree/column-width-build)
   *
   * Zipcelx should be either in global scope (window) or can be provided with {@link #config-zipcelx} config.
   * Reference to zipcelx library. If not provided, exporter will look in the global scope.
   * @config {Object}
   * @deprecated 6.1.0 Use {@link #config-xlsProvider} instead
   */
  zipcelx: null,
  /**
   * If this config is true, exporter will convert all empty values to ''. Empty values are:
   * * undefined, null, NaN
   * * Objects/class instances that do not have toString method defined and are stringified to [object Object]
   * * functions
   * @config {Boolean}
   */
  convertEmptyValueToEmptyString: true,
  /**
   * Set this config to `true` to export all {@link Grid.column.Column#config-exportable} columns including
   * {@link Grid.column.Column#config-hidden} columns as well.
   * @config {Boolean}
   * @default
   */
  exportAllColumns: false
});
ExcelExporter._$name = "ExcelExporter";
GridFeatureManager.registerFeature(ExcelExporter, false, "Grid");

// ../Grid/lib/Grid/feature/experimental/FileDrop.js
var FileDrop = class extends InstancePlugin {
  construct(client, config) {
    const me = this;
    super.construct(client, config);
    EventHelper.on({
      element: client.element,
      thisObj: me,
      drop: me.onFileDrop,
      dragover: me.onFileDragOver,
      dragenter: me.onFileDragEnter,
      dragleave: me.onFileDragLeave
    });
  }
  onFileLoad(domEvent) {
    this.client.trigger("fileDrop", {
      file: this.file,
      domEvent
    });
  }
  onFileDragEnter() {
    this.client.element.classList.add("b-dragging-file");
  }
  onFileDragOver(event) {
    event.preventDefault();
  }
  onFileDragLeave(event) {
    const { element } = this.client;
    if (event.relatedTarget && !element.contains(event.relatedTarget)) {
      this.client.element.classList.remove("b-dragging-file");
    }
  }
  onFileDrop(domEvent) {
    domEvent.preventDefault();
    const file = domEvent.dataTransfer.items[0].getAsFile();
    this.client.trigger("fileDrop", { file, domEvent });
    this.onFileDragLeave(domEvent);
  }
};
__publicField(FileDrop, "$name", "FileDrop");
FileDrop._$name = "FileDrop";
GridFeatureManager.registerFeature(FileDrop, false, "Grid");

// ../Grid/lib/Grid/row/Row.js
var cellContentRange = document.createRange();
var Row = class extends Base {
  //region Init
  /**
   * Constructs a Row setting its index.
   * @param {Object} config A configuration object which must contain the following two properties:
   * @param {Grid.view.Grid} config.grid The owning Grid.
   * @param {Grid.row.RowManager} config.rowManager The owning RowManager.
   * @param {Number} config.index The index of the row within the RowManager's cache.
   * @function constructor
   * @internal
   */
  construct(config) {
    Object.assign(this, {
      _elements: {},
      _elementsArray: [],
      _cells: {},
      _allCells: [],
      _regions: [],
      lastHeight: 0,
      lastTop: -1,
      _dataIndex: 0,
      _top: 0,
      _height: 0,
      _id: null,
      forceInnerHTML: false,
      isGroupFooter: false,
      // Create our cell rendering context
      cellContext: new GridLocation({
        grid: config.grid,
        id: null,
        columnIndex: 0
      })
    });
    super.construct(config);
    if (this.grid.positionMode === "position") {
      this.translateElements = this.positionElements;
    }
  }
  doDestroy() {
    const me = this;
    if (!me.rowManager.isDestroying) {
      me.removeElements();
      if (me.rowManager.idMap[me.id] === me) {
        delete me.rowManager.idMap[me.id];
      }
    }
    super.doDestroy();
  }
  //endregion
  //region Data getters/setters
  /**
   * Get index in RowManagers rows array
   * @property {Number}
   * @readonly
   */
  get index() {
    return this._index;
  }
  set index(index) {
    this._index = index;
  }
  /**
   * Get/set this rows current index in grids store
   * @property {Number}
   */
  get dataIndex() {
    return this._dataIndex;
  }
  set dataIndex(dataIndex) {
    if (this._dataIndex !== dataIndex) {
      this._dataIndex = dataIndex;
      this.eachElement((element) => {
        element.dataset.index = dataIndex;
        element.ariaRowIndex = this.grid.hideHeaders ? dataIndex + 1 : dataIndex + 2;
      });
    }
  }
  /**
   * Get/set id for currently rendered record
   * @property {String|Number}
   */
  get id() {
    return this._id;
  }
  set id(id2) {
    const me = this, idObj = { id: id2 }, idMap = me.rowManager.idMap;
    if (me._id !== id2 || idMap[id2] !== me) {
      if (idMap[me._id] === me)
        delete idMap[me._id];
      idMap[id2] = me;
      me._id = id2;
      me.eachElement((element) => {
        DomDataStore.assign(element, idObj);
        element.dataset.id = id2;
      });
      me.eachCell((cell) => DomDataStore.assign(cell, idObj));
    }
  }
  //endregion
  //region Row elements
  /**
   * Add a row element for specified region.
   * @param {String} region Region to add element for
   * @param {HTMLElement} element Element
   * @private
   */
  addElement(region, element) {
    const me = this, { dataIndex } = me;
    let cellElement = element.firstElementChild;
    me._elements[region] = element;
    me._elementsArray.push(element);
    me._regions.push(region);
    DomDataStore.assign(element, { index: me.index });
    me._cells[region] = [];
    while (cellElement) {
      const { column, columnId } = cellElement.dataset;
      me._cells[region].push(cellElement);
      me._allCells.push(cellElement);
      DomDataStore.set(cellElement, {
        column,
        columnId,
        rowElement: cellElement.parentNode,
        row: me
      });
      cellElement = cellElement.nextElementSibling;
    }
    element.dataset.index = dataIndex;
    element.ariaRowIndex = me.grid.hideHeaders ? dataIndex + 1 : dataIndex + 2;
    me.lastTop = null;
    if (me.top !== null) {
      me.translateElements(true);
    }
  }
  /**
   * Get the element for the specified region.
   * @param {String} region
   * @returns {HTMLElement}
   */
  getElement(region) {
    return this._elements[region];
  }
  /**
   * Get the {@link Core.helper.util.Rectangle element bounds} for the specified region of this Row.
   * @param {String} region
   * @returns {Core.helper.util.Rectangle}
   */
  getRectangle(region) {
    return Rectangle.from(this.getElement(region));
  }
  /**
   * Execute supplied function for each regions element.
   * @param {Function} fn
   */
  eachElement(fn2) {
    this._elementsArray.forEach(fn2);
  }
  /**
   * Execute supplied function for each cell.
   * @param {Function} fn
   */
  eachCell(fn2) {
    this._allCells.forEach(fn2);
  }
  /**
   * An object, keyed by region name (for example `locked` and `normal`) containing the elements which comprise the full row.
   * @type {Object<String,HTMLElement>}
   * @readonly
   */
  get elements() {
    return this._elements;
  }
  /**
   * The row element, only applicable when not using multiple grid sections (see {@link #property-elements})
   * @type {HTMLElement}
   * @readonly
   */
  get element() {
    const region = Object.keys(this._elements)[0];
    return this._elements[region];
  }
  //endregion
  //region Cell elements
  /**
   * Row cell elements
   * @property {HTMLElement[]}
   * @readonly
   */
  get cells() {
    return this._allCells;
  }
  /**
   * Get cell elements for specified region.
   * @param {String} region Region to get elements for
   * @returns {HTMLElement[]} Array of cell elements
   */
  getCells(region) {
    return this._cells[region];
  }
  /**
   * Get the cell element for the specified column.
   * @param {String|Number} columnId Column id
   * @returns {HTMLElement} Cell element
   */
  getCell(columnId, strict = false) {
    return this._allCells.find((cell) => {
      const cellData = DomDataStore.get(cell);
      return cellData.columnId == columnId || !strict && cellData.column == columnId;
    });
  }
  removeElements(onlyRelease = false) {
    const me = this;
    me.rowManager.trigger("removeRow", { row: me });
    if (!onlyRelease) {
      me.eachElement((element) => element.remove());
    }
    me._elements = {};
    me._cells = {};
    me._elementsArray.length = me._regions.length = me._allCells.length = me.lastHeight = me.height = 0;
    me.lastTop = -1;
  }
  //endregion
  //region Height
  /**
   * Get/set row height
   * @property {Number}
   */
  get height() {
    return this._height;
  }
  set height(height) {
    if (!this.cls["b-rowexpander-row-expanded"]) {
      this.setProperty("--b-grid-row-height", height === this.grid.rowHeight ? "" : DomHelper.setLength(height));
    }
    this._height = height;
  }
  /**
   * Get row height including border
   * @property {Number}
   */
  get offsetHeight() {
    return this.height + this.grid._rowBorderHeight;
  }
  /**
   * Sync elements height to rows height
   * @private
   */
  updateElementsHeight(isExport) {
    const me = this;
    if (!isExport) {
      me.rowManager.storeKnownHeight(me.id, me.height);
    }
    if (me.lastHeight !== me.height) {
      this.eachElement((element) => element.style.height = `${me.offsetHeight}px`);
      me.lastHeight = me.height;
    }
  }
  //endregion
  //region CSS
  /**
   * Add CSS classes to each element.
   * @param {...String|Object<String,Boolean|Number>|Core.helper.util.DomClassList} classes
   */
  addCls(classes) {
    this.updateCls(this.cls.add(classes));
  }
  /**
   * Remove CSS classes from each element.
   * @param {...String|Object<String,Boolean|Number>|Core.helper.util.DomClassList} classes
   */
  removeCls(classes) {
    this.updateCls(this.cls.remove(classes));
  }
  /**
   * Toggle CSS classes for each element.
   * @param {Object<String,Boolean|Number>|Core.helper.util.DomClassList|...String} classes
   * @param {Boolean} add
   */
  toggleCls(classes, add) {
    this.updateCls(this.cls[add ? "add" : "remove"](classes));
  }
  /**
   * Adds/removes class names according to the passed object's properties.
   *
   * Properties with truthy values are added.
   * Properties with false values are removed.
   * @param {Object<String,Boolean|Number>} classes Object containing properties to set/clear
   */
  assignCls(classes) {
    this.updateCls(this.cls.assign(classes));
  }
  changeCls(cls) {
    return new DomClassList(cls);
  }
  updateCls(cls) {
    this.eachElement((element) => DomHelper.syncClassList(element, cls));
  }
  setAttribute(attribute, value2) {
    this.eachElement((element) => element.setAttribute(attribute, value2));
  }
  removeAttribute(attribute) {
    this.eachElement((element) => element.removeAttribute(attribute));
  }
  setProperty(property, value2) {
    this.eachElement((element) => element.style.setProperty(property, value2));
  }
  //endregion
  //region Position
  /**
   * Is this the very first row?
   * @property {Boolean}
   * @readonly
   */
  get isFirst() {
    return this.dataIndex === 0;
  }
  /**
   * Row top coordinate
   * @property {Number}
   * @readonly
   */
  get top() {
    return this._top;
  }
  /**
   * Row bottom coordinate
   * @property {Number}
   * @readonly
   */
  get bottom() {
    return this._top + this._height + this.grid._rowBorderHeight;
  }
  /**
   * Sets top coordinate, translating elements position.
   * @param {Number} top Top coordinate
   * @param {Boolean} [silent] Specify `true` to not trigger translation event
   * @internal
   */
  setTop(top, silent) {
    if (this._top !== top) {
      this._top = top;
      this.translateElements(silent);
    }
  }
  /**
   * Sets bottom coordinate, translating elements position.
   * @param {Number} bottom Bottom coordinate
   * @param {Boolean} [silent] Specify `true` to not trigger translation event
   * @private
   */
  setBottom(bottom, silent) {
    this.setTop(bottom - this.offsetHeight, silent);
  }
  // Used by export feature to position individual row
  translate(top, silent = false) {
    this.setTop(top, silent);
    return top + this.offsetHeight;
  }
  /**
   * Sets css transform to position elements at correct top position (translateY)
   * @private
   */
  translateElements(silent) {
    const me = this, { top, _elementsArray } = me;
    if (me.lastTop !== top) {
      for (let i = 0, { length } = _elementsArray; i < length; i++) {
        _elementsArray[i].style.transform = `translate(0,${top}px)`;
      }
      !silent && me.rowManager.trigger("translateRow", { row: me });
      me.lastTop = top;
    }
  }
  /**
   * Sets css top to position elements at correct top position
   * @private
   */
  positionElements(silent) {
    const me = this, { top, _elementsArray } = me;
    if (me.lastTop !== top) {
      for (let i = 0, { length } = _elementsArray; i < length; i++) {
        _elementsArray[i].style.top = `${top}px`;
      }
      !silent && me.rowManager.trigger("translateRow", { row: me });
      me.lastTop = top;
    }
  }
  /**
   * Moves all row elements up or down and updates model.
   * @param {Number} offsetTop Pixels to offset the elements
   * @private
   */
  offset(offsetTop) {
    let newTop = this._top + offsetTop;
    if (newTop < 0) {
      offsetTop -= newTop;
      newTop = 0;
    }
    this.setTop(newTop);
    return offsetTop;
  }
  //endregion
  //region Render
  /**
   * When the Store has `lazyLoad` set to `true` and the record is waiting to be loaded, a "skeleton" placeholder is
   * rendered while waiting.
   * @param recordIndex
   * @param recordPromise
   * @private
   */
  renderPlaceholder(recordIndex, recordPromise, updatingSingleRow, batch, isExport) {
    const me = this, { rowManager } = me, { rowHeight } = rowManager;
    me.addCls("b-row-placeholder");
    if (me.height !== rowHeight) {
      me.height = rowHeight;
      me.updateElementsHeight(true);
      rowManager.bufferedTranslateFromRow(me);
    }
    me.dataIndex = recordIndex;
    recordPromise.then((record) => {
      if (record && me.dataIndex === recordIndex && me.grid.store.indexOf(record) === recordIndex) {
        me.render(recordIndex, record, updatingSingleRow, batch, isExport, true);
      }
    });
  }
  /**
   * Renders a record into this row´s elements (trigger event that subgrids catch to do the actual rendering).
   * @param {Number} recordIndex
   * @param {Core.data.Model} record
   * @param {Boolean} [updatingSingleRow]
   * @param {Boolean} [batch]
   * @private
   */
  render(recordIndex, record, updatingSingleRow = true, batch = false, isExport = false, isAsync = false) {
    var _a5, _b2, _c2, _d;
    const me = this, {
      cellContext,
      cls,
      elements,
      grid,
      rowManager,
      height: oldHeight,
      _id: oldId
    } = me, rowElData = DomDataStore.get(me._elementsArray[0]), rowHeight = rowManager._rowHeight, { store } = grid, isTree = grid.features.tree && store.isTree;
    let i = 0, size;
    if (!record && record !== false) {
      if (recordIndex != null) {
        record = (_a5 = store.lazyLoad) == null ? void 0 : _a5.getAt(recordIndex);
        if (record && !record.isModel) {
          return me.renderPlaceholder(recordIndex, record, updatingSingleRow, batch, isExport);
        }
      }
      if (!record) {
        record = store.getById(rowElData.id);
        recordIndex = store.indexOf(record);
      }
    }
    if (!record) {
      return;
    }
    rowManager.updatingSingleRow = updatingSingleRow;
    me.removeCls("b-row-placeholder");
    const rCls = record == null ? void 0 : record.cls, recordCls = rCls ? rCls.isDomClassList ? rCls : new DomClassList(rCls) : null;
    cls.assign({
      // do not put updating class if we're exporting the row
      "b-grid-row-updating": updatingSingleRow && grid.transitionDuration && !isExport,
      "b-selected": grid.isSelected(record == null ? void 0 : record.id),
      "b-readonly": record.readOnly,
      "b-linked": record.isLinked,
      "b-original": record.hasLinks
    });
    if (me.lastRecordCls) {
      cls.remove(me.lastRecordCls);
    }
    if (recordCls) {
      cls.add(recordCls);
      me.lastRecordCls = Object.assign({}, recordCls);
    } else {
      me.lastRecordCls = null;
    }
    rowManager.trigger("beforeRenderRow", { row: me, record, recordIndex, oldId });
    grid.beforeRenderRow({ row: me, record, recordIndex, oldId, cls });
    me.updateCls(cls);
    if (updatingSingleRow && grid.transitionDuration && !isExport) {
      grid.setTimeout(() => {
        if (!me.isDestroyed) {
          cls.remove("b-grid-row-updating");
          me.updateCls(cls);
        }
      }, grid.transitionDuration + 50);
    }
    me.id = record.id;
    me.dataIndex = recordIndex;
    const height = !grid.fixedRowHeight && grid.getRowHeight(record) || rowHeight;
    let maxRequestedHeight = me.maxRequestedHeight = null;
    if (isTree) {
      for (const region in elements) {
        const el = elements[region];
        el.id = `${grid.id}-${region}-${me.id}`;
        DomHelper.setAttributes(el, {
          "aria-level": record.childLevel + 1,
          "aria-setsize": record.parent.children.length,
          "aria-posinset": record.parentIndex + 1
        });
        if (record.isExpanded(store)) {
          DomHelper.setAttributes(el, {
            "aria-expanded": true,
            // A branch node may be configured expanded, but yet have no children.
            // They may be added dynamically.
            "aria-owns": ((_b2 = record.children) == null ? void 0 : _b2.length) ? (_c2 = record.children) == null ? void 0 : _c2.slice(0, 50).map((r) => `${grid.id}-${region}-${r.id}`).join(" ") : null
          });
        } else {
          if (record.isLeaf) {
            el.removeAttribute("aria-expanded");
          } else {
            el.setAttribute("aria-expanded", false);
          }
          el.removeAttribute("aria-owns");
        }
      }
    }
    cellContext._record = record;
    cellContext._id = record.id;
    cellContext._rowIndex = recordIndex;
    for (i = 0; i < grid.columns.visibleColumns.length; i++) {
      const column = grid.columns.visibleColumns[i];
      cellContext._columnId = column.id;
      cellContext._column = column;
      cellContext._columnIndex = i;
      cellContext._cell = me.getCell(column.id, true);
      cellContext.height = height;
      cellContext.maxRequestedHeight = maxRequestedHeight;
      cellContext.updatingSingleRow = updatingSingleRow;
      size = me.renderCell(cellContext);
      if (!rowManager.fixedRowHeight) {
        if (size.height != null) {
          maxRequestedHeight = Math.max(maxRequestedHeight, size.height);
          if (!size.transient) {
            me.maxRequestedHeight = maxRequestedHeight;
          }
        }
      }
    }
    const useHeight = maxRequestedHeight != null ? maxRequestedHeight : height;
    me.height = (_d = grid.processRowHeight(record, useHeight)) != null ? _d : useHeight;
    me.updateElementsHeight(isExport);
    if (isAsync && oldHeight !== me.height) {
      rowManager.bufferedTranslateFromRow(me, batch);
    }
    if (updatingSingleRow && !isExport) {
      if (oldHeight !== me.height) {
        rowManager.translateFromRow(me, batch);
      }
      rowManager.trigger("updateRow", { row: me, record, recordIndex, oldId });
      rowManager.trigger("renderDone");
    }
    grid.afterRenderRow({ row: me, record, recordIndex, oldId, oldHeight, isExport });
    rowManager.trigger("renderRow", { row: me, record, recordIndex, oldId, isExport });
    if (oldHeight && me.height !== oldHeight) {
      rowManager.trigger("rowRowHeight", { row: me, record, height: me.height, oldHeight });
    }
    me.forceInnerHTML = false;
    rowManager.updatingSingleRow = false;
  }
  /**
   * Renders a single cell, calling features to allow them to hook
   * @param {Grid.util.GridLocation|HTMLElement|Object} cellContext A {@link Grid.util.GridLocation} which contains
   *   rendering options, or a cell element, which can be used to initialize a {@link Grid.util.GridLocation}
   * @param {Number} [cellContext.height] Configured row height
   * @param {Number} [cellContext.maxRequestedHeight] Maximum proposed row height from renderers
   * @param {Boolean} [cellContext.updatingSingleRow] Rendered as part of updating a single row
   * @param {Boolean} [cellContext.isMeasuring] Rendered as part of a measuring operation
   * @internal
   */
  renderCell(cellContext) {
    var _a5;
    if (!cellContext.isLocation) {
      cellContext = new GridLocation(cellContext);
    }
    let {
      cell: cellElement,
      record
    } = cellContext;
    const {
      grid,
      column,
      height,
      maxRequestedHeight,
      updatingSingleRow = true,
      isMeasuring = false
    } = cellContext, cellEdit = (_a5 = grid.features) == null ? void 0 : _a5.cellEdit, cellElementData = DomDataStore.get(cellElement), rowElement = cellElementData.rowElement, rowElementData = DomDataStore.get(rowElement);
    if (!record) {
      record = cellContext._record = grid.store.getById(rowElementData.id);
      if (!record) {
        return;
      }
    }
    let cellContent = column.getRawValue(record);
    const dataField = record.fieldMap[column.field], size = { configuredHeight: height, height: null, maxRequestedHeight }, cellCls = column.getCellClass(cellContext), rendererData = {
      cellElement,
      cellCls,
      dataField,
      rowElement,
      value: cellContent,
      record,
      column,
      size,
      grid,
      row: cellElementData.row,
      updatingSingleRow,
      isMeasuring,
      isExport: false
    }, useRenderer = column.renderer;
    if (column.readOnly || record.readOnly || grid.readOnly) {
      cellElement.setAttribute("aria-readonly", true);
    } else {
      cellElement.removeAttribute("aria-readonly");
    }
    grid.beforeRenderCell(rendererData);
    if (rendererData.cellElement !== cellElement) {
      cellElement = rendererData.cellElement;
    }
    DomHelper.syncClassList(cellElement, cellCls);
    let shouldSetContent = true;
    if (!Objects.isPromise(cellContent)) {
      if (useRenderer) {
        cellContent = column.callback(useRenderer, column, [rendererData]);
        if (cellContent === void 0) {
          if (record.generatedParent && column.rendererReturningContent) {
            cellContent = "";
          } else if (column.alwaysClearCell === false) {
            shouldSetContent = false;
          }
        }
      } else if (dataField) {
        cellContent = dataField.print(cellContent);
      }
    }
    const cellUpdateContext = {
      cellContent,
      size,
      grid,
      isMeasuring,
      cellContext,
      cellElementData,
      rendererData,
      shouldSetContent,
      cellEdit,
      record,
      column,
      cellElement
    };
    if (Objects.isPromise(cellContent)) {
      cellElement.classList.add("b-loading-cell");
      cellContent.then((cellContent2) => {
        if (!this.isDestroyed) {
          cellElement.classList.remove("b-loading-cell");
          if (grid.getRecordFromElement(cellElement) === record) {
            this.updateCellContent({ ...cellUpdateContext, cellContent: cellContent2 });
          }
        }
      });
      return size;
    }
    return this.updateCellContent(cellUpdateContext);
  }
  updateCellContent({
    cellContent,
    size,
    grid,
    isMeasuring,
    cellContext,
    cellElementData,
    rendererData,
    shouldSetContent,
    cellEdit,
    record,
    column,
    cellElement
  }) {
    var _a5, _b2, _c2;
    const hasFrameworkRenderer = (_a5 = grid.hasFrameworkRenderer) == null ? void 0 : _a5.call(grid, { cellContent, column });
    if (hasFrameworkRenderer && record.isSpecialRow) {
      cellContent = "";
    }
    const frameworkPerformsFullRendering = hasFrameworkRenderer && !column.data.tree && !record.isSpecialRow;
    if (shouldSetContent && !frameworkPerformsFullRendering) {
      let renderTarget = cellElement;
      if (((_b2 = cellEdit == null ? void 0 : cellEdit.editorContext) == null ? void 0 : _b2.column) === column && !cellEdit.editor.isFinishing) {
        if (cellEdit == null ? void 0 : cellEdit.editorContext.equals(cellContext)) {
          renderTarget = this.moveContentFromCell(cellElement, cellEdit.editor.element);
        } else {
          renderTarget.classList.remove("b-editing");
        }
      }
      const hasObjectContent = cellContent != null && typeof cellContent === "object", hasStringContent = typeof cellContent === "string", text = hasObjectContent || cellContent == null ? "" : String(cellContent);
      if (this.forceInnerHTML) {
        renderTarget.innerHTML = "";
        delete renderTarget._content;
        cellElement.lastDomConfig = null;
      }
      if (!hasObjectContent && column.htmlEncode && !column.disableHtmlEncode) {
        if (cellElement._hasHtml) {
          renderTarget.innerText = text;
          cellElement._hasHtml = false;
        } else {
          DomHelper.setInnerText(renderTarget, text);
        }
        delete renderTarget._content;
      } else {
        if (column.autoSyncHtml && (!hasStringContent || DomHelper.getChildElementCount(renderTarget))) {
          if (hasStringContent) {
            DomHelper.sync(text, renderTarget.firstElementChild);
          } else if (hasObjectContent) {
            DomSync.sync({
              domConfig: cellContent,
              targetElement: renderTarget
            });
            delete renderTarget._content;
          }
        } else if (hasObjectContent) {
          DomSync.sync({
            targetElement: renderTarget,
            domConfig: {
              onlyChildren: true,
              children: ArrayHelper.asArray(cellContent)
            }
          });
          delete renderTarget._content;
        } else if (renderTarget._content !== text) {
          renderTarget.innerHTML = renderTarget._content = text;
        }
      }
      if (renderTarget !== cellElement) {
        const { firstChild } = cellElement;
        for (const node of [...renderTarget.childNodes]) {
          cellElement.insertBefore(node, firstChild);
        }
        cellElement.classList.add("b-editing", "b-focused");
      }
    }
    if (!record.isSpecialRow) {
      (_c2 = grid.processCellContent) == null ? void 0 : _c2.call(grid, {
        cellElementData,
        rendererData,
        // In case of TreeColumn we should prerender inner cell content like expand controls, bullets, etc
        // Then the framework renders the content into the nested "b-tree-cell-value" element.
        // rendererHtml is set in TreeColumn.treeRenderer
        rendererHtml: rendererData.rendererHtml || cellContent
      });
    }
    if (column.autoHeight && !record.rowHeight && size.height == null) {
      cellElement.classList.add("b-measuring-auto-height");
      size.height = Math.max(cellElement.offsetHeight, grid.rowHeight);
      cellElement.classList.remove("b-measuring-auto-height");
    }
    if (!isMeasuring) {
      this.rowManager.trigger("renderCell", rendererData);
    }
    if (column.afterRenderCell) {
      column.callback(column.afterRenderCell, column, [rendererData]);
    }
    return size;
  }
  //#region Hooks for salesforce
  moveContentFromCell(cellElement, editorElement) {
    if (!editorElement.parentNode) {
      cellElement.appendChild(editorElement);
    }
    cellContentRange.setStart(cellElement, 0);
    cellContentRange.setEndBefore(editorElement);
    const renderTarget = document.createElement("div");
    renderTarget.appendChild(cellContentRange.extractContents());
    renderTarget.classList.add("b-editing");
    return renderTarget;
  }
  //#endregion
  //endregion
};
__publicField(Row, "$name", "Row");
__publicField(Row, "configurable", {
  /**
   * When __read__, this a {@link Core.helper.util.DomClassList} of class names to be
   * applied to this Row's elements.
   *
   * It can be __set__ using Object notation where each property name with a truthy value is added as
   * a class, or as a regular space-separated string.
   *
   * @member {Core.helper.util.DomClassList} cls
   * @accepts {Core.helper.util.DomClassList|Object<String,Boolean|Number>}
   */
  /**
   * The class name to initially add to all row elements
   * @config {String|Core.helper.util.DomClassList|Object<String,Boolean|Number>}
   */
  cls: {
    $config: {
      equal: (c1, c2) => (c1 == null ? void 0 : c1.isDomClassList) && (c2 == null ? void 0 : c2.isDomClassList) && c1.isEqual(c2)
    }
  }
});
Row.initClass();
Row._$name = "Row";

// ../Grid/lib/Grid/view/Bar.js
var Bar = class extends Widget {
  static get defaultConfig() {
    return {
      htmlCls: "",
      scrollable: {
        overflowX: "hidden-scroll"
      },
      // Avoid the default role="presentation" which is invalid here
      role: null
    };
  }
  //region Init
  get columns() {
    return this._columns || this.subGrid.columns;
  }
  // Only needed for tests which create standalone Headers with no owning SubGrid.
  set columns(columns) {
    this._columns = columns;
  }
  //endregion
  /**
   * Fix cell widths (flex or fixed width) after rendering.
   * Not a part of template any longer because of CSP
   * @private
   */
  fixCellWidths() {
    const me = this, { hasFlex: hasFlex2 } = me.columns;
    let flexBasis;
    me.columns.traverse((column) => {
      const cellEl = me.getBarCellElement(column.id), domWidth = DomHelper.setLength(column.width), domMinWidth = DomHelper.setLength(column.minWidth), domMaxWidth = DomHelper.setLength(column.maxWidth);
      if (cellEl) {
        flexBasis = domWidth;
        cellEl.style.maxWidth = domMaxWidth;
        if (column.isParent && column.width == null && column.flex == null) {
          const flex = column.children.reduce((result, child) => result += !child.hidden && child.flex || 0, 0);
          cellEl.style.flex = flex > 0 ? `${flex} 0 auto` : "";
          cellEl.style.minWidth = null;
          if (flex > 0) {
            column.traverse((col) => col.data.minWidth = null);
          }
        } else {
          if (parseInt(column.minWidth) >= 0) {
            cellEl.style.minWidth = domMinWidth;
          }
          cellEl.style.flex = cellEl.style.flexBasis = cellEl.style.width = "";
          if (column.flex) {
            if (!isNaN(parseInt(column.flex)) && column.children) {
              cellEl.style.flex = `${column.flex} 0 auto`;
            } else {
              cellEl.style.flex = column.flex;
            }
          } else if (parseInt(column.width) >= 0) {
            const parent = column.parent;
            if (me.isHeader && !parent.isRoot && !parent.width) {
              cellEl.style.width = domWidth;
            } else {
              cellEl.style.flexBasis = flexBasis;
            }
          }
        }
        if (column.height >= 0) {
          cellEl.style.height = DomHelper.setLength(column.height);
        }
      }
    });
    me.scrollable.element.classList.toggle("b-has-flex", hasFlex2);
  }
  getLrPadding(cellEl) {
    if (!this.cellLrPadding) {
      const s = cellEl.ownerDocument.defaultView.getComputedStyle(cellEl);
      this.cellLrPadding = parseInt(s.getPropertyValue("padding-left")) + parseInt(s.getPropertyValue("padding-right")) + parseInt(s.getPropertyValue("border-left-width")) + parseInt(s.getPropertyValue("border-right-width"));
    }
    return this.cellLrPadding;
  }
  /**
   * Get the header or footer cell element for the specified column.
   * @param {String} columnId Column id
   * @returns {HTMLElement} Header or footer element, depending on which subclass is in use.
   * @private
   */
  getBarCellElement(columnId) {
    return this.element.querySelector(`[data-column-id="${columnId}"]`);
  }
};
__publicField(Bar, "$name", "Bar");
// Factoryable type name
__publicField(Bar, "type", "gridbar");
Bar.initClass();
Bar._$name = "Bar";

// ../Grid/lib/Grid/view/Footer.js
var Footer = class extends Bar {
  get subGrid() {
    return this._subGrid;
  }
  set subGrid(subGrid) {
    this._subGrid = this.owner = subGrid;
  }
  refreshContent() {
    this.element.firstElementChild.innerHTML = this.contentTemplate();
    this.fixFooterWidths();
  }
  onInternalPaint({ firstPaint }) {
    if (firstPaint) {
      this.refreshContent();
    }
  }
  template() {
    const region = this.subGrid.region;
    return TemplateHelper.tpl`
            <div class="b-grid-footer-scroller b-grid-footer-scroller-${region}" role="presentation">
                <div data-reference="footersElement" class="b-grid-footers b-grid-footers-${region}" data-region="${region}" role="presentation"></div>
            </div>
        `;
  }
  get overflowElement() {
    return this.footersElement;
  }
  //region Getters
  /**
   * Get the footer cell element for the specified column.
   * @param {String} columnId Column id
   * @returns {HTMLElement} Footer cell element
   */
  getFooter(columnId) {
    return this.getBarCellElement(columnId);
  }
  //endregion
  /**
   * Footer template. Iterates leaf columns to create content.
   * Style not included because of CSP. Widths are fixed up in
   * {@link #function-fixFooterWidths}
   * @private
   */
  contentTemplate() {
    const me = this;
    return me.columns.visibleColumns.map((column) => {
      return TemplateHelper.tpl`
                <div
                    class="b-grid-footer ${column.align ? `b-grid-footer-align-${column.align}` : ""} ${column.cls || ""}"
                    data-column="${column.field || ""}" data-column-id="${column.id}" data-all-index="${column.allIndex}"
                    role="gridcell">
                    ${column.footerText || ""}
                </div>`;
    }).join("");
  }
  /**
   * Fix footer widths (flex or fixed width) after rendering. Not a part of template any longer because of CSP
   * @private
   */
  fixFooterWidths() {
    this.fixCellWidths();
  }
};
__publicField(Footer, "$name", "Footer");
// Factoryable type name
__publicField(Footer, "type", "gridfooter");
Footer.initClass();
Footer._$name = "Footer";

// ../Grid/lib/Grid/row/RowManager.js
var cloneRowEl = (el) => {
  const p = el.parentElement, result = el.cloneNode(true);
  result.classList.add("b-removing");
  p.insertBefore(result, el);
  return result;
};
var nodeAnimationCleanup;
var nodeAnimationCleanupListener;
var RowManager = class extends InstancePlugin.mixin(Delayable_default) {
  //region Config
  // Plugin configuration.
  static get pluginConfig() {
    return {
      chain: [
        "destroy"
      ],
      assign: [
        "topRow",
        "bottomRow",
        "firstVisibleRow",
        "lastVisibleRow",
        "firstFullyVisibleRow",
        "lastFullyVisibleRow",
        "getRowById",
        "getRecordCoords",
        "getRow",
        "getRowFor",
        "getRowFromElement"
      ]
    };
  }
  static get defaultConfig() {
    return {
      rowClass: Row,
      /**
       * Number of rows to render above current viewport
       * @config {Number}
       * @default
       */
      prependRowBuffer: 5,
      /**
       * Number of rows to render below current viewport
       * @config {Number}
       * @default
       */
      appendRowBuffer: 5,
      /**
       * Default row height, assigned from Grid at construction (either from config
       * {@link Grid.view.Grid#config-rowHeight} or CSS). Can be set from renderers
       * @config {Number}
       * @default
       */
      rowHeight: null,
      /**
       * Set to `true` to get a small performance boost in applications that uses fixed row height
       * @config {Boolean}
       */
      fixedRowHeight: null,
      autoHeight: false
    };
  }
  static get properties() {
    return {
      idMap: {},
      topIndex: 0,
      lastScrollTop: 0,
      _rows: [],
      // Record id -> row height mapping
      heightMap: /* @__PURE__ */ new Map(),
      // Sum of entries in heightMap
      totalKnownHeight: 0,
      // Will be calculated in `estimateTotalHeight()`, as totalKnownHeight + an estimate for unknown rows
      _totalHeight: 0,
      // Average of the known heights, kept up to date when entries in the heightMap are updated
      averageRowHeight: 0,
      scrollTargetRecordId: null,
      refreshDetails: {
        topRowIndex: 0,
        topRowTop: 0
      }
    };
  }
  //endregion
  //region Init
  construct(config) {
    config.grid._rowManager = this;
    super.construct(config.grid, config);
  }
  // Chained to client grid's doDestroy
  doDestroy() {
    this.removeAllRows();
    super.doDestroy();
  }
  /**
   * Initializes the RowManager with Rows to fit specified height.
   * @param {Number} height
   * @param {Boolean} [isRendering]
   * @private
   * @category Init
   */
  initWithHeight(height, isRendering = false) {
    const me = this;
    if (me.autoHeight) {
      height = me.store.count * me.preciseRowOffsetHeight;
    }
    me.viewHeight = height;
    me.calculateRowCount(isRendering);
    return height;
  }
  /**
   * Releases all elements (not from dom), calculates how many are needed, creates those and renders
   */
  reinitialize(returnToTop = false) {
    const me = this;
    me.calculateRowCount(false, true, true);
    if (me.topIndex + me.rowCount - 1 > me.storeCount) {
      returnToTop = true;
    }
    if (returnToTop) {
      me.topIndex = me.lastScrollTop = 0;
    }
    me.scrollTargetRecordId = null;
    const { topRow } = me;
    if (topRow) {
      topRow.dataIndex = me.topIndex;
      topRow.setTop(me.calculateTop(me.topIndex), true);
    }
    me.estimateTotalHeight();
    me.renderFromRow(topRow);
  }
  //endregion
  //region Rows
  /**
   * Add or remove rows to fit row count
   * @private
   * @category Rows
   */
  matchRowCount(skipRender = false) {
    var _a5;
    const me = this, { rows, grid, rowClass } = me, numRows = rows.length, delta = numRows - me.rowCount;
    if (delta) {
      if (delta < 0) {
        const newRows = [];
        for (let index = numRows, dataIndex = numRows ? rows[numRows - 1].dataIndex + 1 : 0; index < me.rowCount; index++, dataIndex++) {
          newRows.push(rowClass.new({
            cls: grid.rowCls,
            rowManager: me,
            grid,
            index,
            dataIndex
          }));
        }
        rows.push.apply(rows, newRows);
        me.trigger("addRows", { rows: newRows });
        if (!skipRender) {
          me.renderFromRow(rows[Math.max(0, numRows - 1)]);
        }
      } else {
        const activeCell = (_a5 = DomHelper.getActiveElement(grid)) == null ? void 0 : _a5.closest(".b-grid-cell"), removedRows = rows.splice(numRows - delta, delta);
        if (numRows !== delta && removedRows.some((row) => row.cells.includes(activeCell))) {
          rows[rows.length - 1].cells[grid.focusedCell.columnIndex].focus();
        }
        me.trigger("removeRows", { rows: removedRows });
        removedRows.forEach((row) => row.destroy());
        !skipRender && rows.length && me.fillBelow(me.lastScrollTop);
      }
    }
  }
  /**
   * Calculates how many rows fit in the available height (view height)
   * @private
   * @category Rows
   */
  calculateRowCount(skipMatchRowCount = false, allowRowCountShrink = true, skipRender = false) {
    var _a5, _b2;
    const me = this, { storeCount } = me, visibleRowCount = Math.ceil(me.viewHeight / me.minRowOffsetHeight), maxRenderRowCount = visibleRowCount + me.prependRowBuffer + me.appendRowBuffer;
    if (!((_a5 = me.grid.columns) == null ? void 0 : _a5.count) || isNaN(visibleRowCount)) {
      me.rowCount = 0;
      return 0;
    }
    if (maxRenderRowCount < me.rowCount && !allowRowCountShrink) {
      return me.rowCount;
    }
    me.visibleRowCount = visibleRowCount;
    me.rowCount = Math.min(storeCount, maxRenderRowCount);
    if (!skipMatchRowCount) {
      if (me.rows && me.rowCount !== me.rows.length) {
        me.matchRowCount(skipRender);
        if (((_b2 = me.bottomRow) == null ? void 0 : _b2.dataIndex) >= storeCount && me.topRow.dataIndex !== 0) {
          const indexDelta = me.bottomRow.dataIndex - storeCount + 1;
          for (const row of me.rows) {
            row.dataIndex -= indexDelta;
          }
          me.topIndex -= indexDelta;
        }
      } else if (!me.rowCount) {
        me.trigger("changeTotalHeight", { totalHeight: me.totalHeight });
      }
      me.grid.toggleEmptyText();
    }
    return me.rowCount;
  }
  /**
   * Animate adding or removing rows to/from the rows array.
   * @internal
   * @param {Number} index The index at which to insert or remove
   * @param {Number} count The number of rows to add. May be negative to remove.
   */
  insert(index, count) {
    var _a5, _b2, _c2, _d, _e;
    nodeAnimationCleanup == null ? void 0 : nodeAnimationCleanup();
    const me = this, {
      rows,
      rowCount,
      rowClass,
      grid
    } = me, { element } = grid, { rowCls } = grid, newRowDataIndex = ((_a5 = rows[index - 1]) == null ? void 0 : _a5.dataIndex) + 1 || 0, addedRows = [], durationMS = DateHelper.as("ms", DomHelper.getStyleValue(grid.element, "--row-splice-duration")), visibleRowsBelow = me.lastVisibleRow.index + 1 - index;
    let needsPrune;
    if (count < 0) {
      count = Math.abs(count);
      const visualRemoveCount = newRowDataIndex >= grid.store.count ? count : Math.min(count, visibleRowsBelow), visuallyRemovedRows = rows.slice(index, index + visualRemoveCount), visuallyRemovedHeight = visualRemoveCount ? visuallyRemovedRows[visuallyRemovedRows.length - 1].bottom - visuallyRemovedRows[0].top : 0, outgoingElements = [], outgoingRows = [], repositioningRows = [];
      for (let i = 0, { length } = visuallyRemovedRows; i < length; i++) {
        const outgoingEls = visuallyRemovedRows[i]._elementsArray.map(cloneRowEl);
        outgoingRows.push(rowClass.new({
          rowManager: me,
          _elementsArray: outgoingEls,
          grid
        }));
        outgoingElements.push(...outgoingEls);
      }
      for (let i = index; i < rowCount; i++) {
        repositioningRows.push(rows[i]);
        rows[i].addCls("b-repositioning");
        rows[i].setTop(rows[i].top + visuallyRemovedHeight);
      }
      needsPrune = rows.length > grid.store.count;
      (_b2 = outgoingElements[0]) == null ? void 0 : _b2.getBoundingClientRect();
      element.classList.add("b-splicing-rows");
      for (let i = 0, { length } = visuallyRemovedRows; i < length; i++) {
        outgoingRows[i].setTop(((_c2 = rows[index - 1]) == null ? void 0 : _c2.bottom) || 0, true);
      }
      nodeAnimationCleanup = () => {
        if (nodeAnimationCleanupListener && !me.isDestroyed) {
          nodeAnimationCleanupListener = nodeAnimationCleanupListener();
          nodeAnimationCleanup = null;
          element.classList.remove("b-splicing-rows");
          outgoingElements.forEach((e) => e.remove());
          repositioningRows.forEach((e) => {
            var _a6;
            return (_a6 = e.removeCls) == null ? void 0 : _a6.call(e, "b-repositioning");
          });
        }
      };
    } else if (count > 0) {
      const repositioningRows = [];
      count = me.grid.hasVerticalOverflow ? Math.min(count, me.lastVisibleRow.index + 1 - index) : Math.min(count, Math.ceil((me.grid.scrollable.clientHeight - (((_d = rows[index - 1]) == null ? void 0 : _d.bottom) || 0)) / me.rowHeight));
      let newIndex = index, dataIndex = newRowDataIndex;
      for (let i = 0; i < count; i++, newIndex++, dataIndex++) {
        addedRows.push(rowClass.new({
          cls: `${rowCls} b-adding`,
          rowManager: me,
          _top: ((_e = rows[index - 1]) == null ? void 0 : _e.bottom) || 0,
          grid,
          index: newIndex,
          dataIndex
        }));
      }
      for (let i = index; i < me.rowCount; i++, newIndex++, dataIndex++) {
        repositioningRows.push(rows[i]);
        rows[i].addCls("b-repositioning");
        rows[i].index = newIndex;
        rows[i].dataIndex = dataIndex;
      }
      needsPrune = addedRows.length;
      nodeAnimationCleanup = () => {
        if (nodeAnimationCleanupListener && !me.isDestroyed) {
          nodeAnimationCleanupListener = nodeAnimationCleanupListener();
          nodeAnimationCleanup = null;
          element.classList.remove("b-splicing-rows");
          addedRows.forEach((r) => !r.isDestroyed && r.removeCls("b-adding"));
          repositioningRows.forEach((e) => {
            var _a6;
            return (_a6 = e.removeCls) == null ? void 0 : _a6.call(e, "b-repositioning");
          });
        }
      };
      rows.splice(index, 0, ...addedRows);
      if (count > 0) {
        me.rowCount += count;
        me.trigger("addRows", { rows: addedRows });
      }
      element.classList.add("b-splicing-rows");
    }
    nodeAnimationCleanupListener = EventHelper.on({
      element: grid.element,
      animationcancel: nodeAnimationCleanup,
      transitioncancel: nodeAnimationCleanup,
      once: true,
      expires: {
        delay: durationMS,
        alt: nodeAnimationCleanup
      }
    });
    me.renderFromRow(rows[index], true);
    if (needsPrune) {
      me.calculateRowCount(false, true, true);
    }
    me.estimateTotalHeight();
    return addedRows;
  }
  removeAllRows() {
    const me = this, { topRow } = me, result = topRow ? me.refreshDetails = {
      topRowIndex: topRow.dataIndex,
      topRowTop: topRow.top
    } : me.refreshDetails, removedRows = me.rows.slice();
    me.trigger("removeRows", { rows: removedRows });
    me.rows.forEach((row) => row.destroy());
    me.rows.length = 0;
    me.idMap = {};
    return result;
  }
  setPosition(refreshDetails) {
    const { topRow } = this, { topRowIndex, topRowTop } = refreshDetails;
    if (topRow) {
      topRow.setTop(topRowTop);
      topRow.dataIndex = topRowIndex;
    }
  }
  //endregion
  //region Rows - Getters
  get store() {
    return this.client.store;
  }
  get storeCount() {
    let { count, lazyLoad } = this.store;
    if (count && lazyLoad && lazyLoad.totalCount == null) {
      count += lazyLoad.chunkSize;
    }
    return count;
  }
  /**
   * Get all Rows
   * @property {Grid.row.Row[]}
   * @readonly
   * @category Rows
   */
  get rows() {
    return this._rows;
  }
  /**
   * Get the Row at a specified store index. Returns `undefined` if the row index is not rendered.
   * @param {Number} index
   * @returns {Grid.row.Row|undefined}
   * @category Rows
   */
  getRow(index) {
    if (this.rowCount) {
      return this.rows[index - this.topIndex];
    }
  }
  /**
   * Get Row for specified record id
   * @param {Core.data.Model|String|Number} recordOrId Record id (or a record)
   * @returns {Grid.row.Row|null} Found Row or null if record not rendered
   * @category Rows
   */
  getRowById(recordOrId) {
    if (recordOrId && recordOrId.isModel) {
      recordOrId = recordOrId.id;
    }
    return this.idMap[recordOrId];
  }
  /**
   * Get a Row from an HTMLElement
   * @param {HTMLElement} element
   * @returns {Grid.row.Row|null} Found Row or null if record not rendered
   * @category Rows
   */
  getRowFromElement(element) {
    element = element.closest(".b-grid-row");
    return element && this.getRow(element.dataset.index);
  }
  /**
   * Get the row at the specified Y coordinate, which is by default viewport-based.
   * @param {Number} y The `Y` coordinate to find the Row for.
   * @param {Boolean} [local=false] Pass `true` if the `Y` coordinate is local to the SubGrid's element.
   * @returns {Grid.row.Row} Found Row or null if no row is rendered at that point.
   */
  getRowAt(y, local = false) {
    if (!local) {
      y -= Rectangle.from(this.grid.bodyContainer, null, true).roundPx(1).top;
      y += this.grid.scrollable.y;
    }
    y = DomHelper.roundPx(y);
    return this.rows.find((r) => y >= r.top && y < r.bottom);
  }
  /**
   * Get a Row for either a record, a record id or an HTMLElement
   * @param {HTMLElement|Core.data.Model|String|Number} recordOrId Record or record id or HTMLElement
   * @returns {Grid.row.Row} Found Row or null if record not rendered
   * @category Rows
   */
  getRowFor(recordOrId) {
    if (recordOrId instanceof HTMLElement) {
      return this.getRowFromElement(recordOrId);
    }
    return this.getRowById(recordOrId);
  }
  /**
   * Gets the Row following the specified Row (by index or object). Wraps around the end.
   * @param {Number|Grid.row.Row} indexOrRow index or Row
   * @returns {Grid.row.Row}
   * @category Rows
   */
  getNextRow(indexOrRow) {
    const index = typeof indexOrRow === "number" ? indexOrRow : indexOrRow.index;
    return this.getRow((index + 1) % this.rowCount);
  }
  /**
   * Get the Row that is currently displayed at top.
   * @property {Grid.row.Row}
   * @readonly
   * @category Rows
   */
  get topRow() {
    return this.rows[0];
  }
  /**
   * Get the Row currently displayed furthest down.
   * @property {Grid.row.Row}
   * @readonly
   * @category Rows
   */
  get bottomRow() {
    const rowCount = Math.min(this.rowCount, this.storeCount);
    return this.rows[rowCount - 1];
  }
  /**
   * Get the topmost visible Row
   * @property {Grid.row.Row}
   * @readonly
   * @category Rows
   */
  get firstVisibleRow() {
    const scrollY = Math.ceil(this.grid.scrollable.y);
    return this.rows.find((row) => row.bottom > scrollY);
  }
  get firstFullyVisibleRow() {
    const scrollY = Math.ceil(this.grid.scrollable.y);
    return this.rows.find((row) => row.top >= scrollY) || this.rows.find((row) => row.top < scrollY && row.bottom > scrollY);
  }
  /**
   * Get the last visible Row
   * @property {Grid.row.Row}
   * @readonly
   * @category Rows
   */
  get lastVisibleRow() {
    const { grid } = this;
    return ArrayHelper.findLast(this.rows, (r) => r.top < grid.scrollable.y + grid.bodyHeight);
  }
  get lastFullyVisibleRow() {
    const { grid } = this;
    return ArrayHelper.findLast(this.rows, (r) => r.bottom < grid.scrollable.y + grid.bodyHeight);
  }
  /**
   * Calls offset() for each Row passing along offset parameter
   * @param {Number} offset Pixels to translate Row elements.
   * @private
   * @category Rows
   */
  offsetRows(offset) {
    if (offset !== 0) {
      const { rows } = this, { length } = rows;
      for (let i = 0; i < length; i++) {
        rows[i].offset(offset);
      }
    }
    this.trigger("offsetRows", { offset });
  }
  //endregion
  //region Row height
  get prependBufferHeight() {
    return this.prependRowBuffer * this.rowOffsetHeight;
  }
  get appendBufferHeight() {
    return this.appendRowBuffer * this.rowOffsetHeight;
  }
  /**
   * Set a fixed row height (can still be overridden by renderers) or get configured row height. Setting refreshes all rows
   * @type {Number}
   * @on-owner
   * @category Rows
   */
  get rowHeight() {
    return this._rowHeight;
  }
  set rowHeight(height) {
    const me = this, { grid, fixedRowHeight } = me, oldHeight = me.rowHeight;
    if (oldHeight === height) {
      return;
    }
    ObjectHelper.assertNumber(height, "rowHeight", true);
    if (height < 10) {
      height = 10;
    }
    me.trigger("beforeRowHeight", { height });
    me.minRowHeight = me._rowHeight = height;
    if (fixedRowHeight) {
      me.averageRowHeight = height;
    }
    if (me.rows.length) {
      const oldY = grid.scrollable.y, topRow = me.getRowAt(oldY, true), edgeOffset = topRow ? topRow.top - oldY : 0;
      let average, oldAverage;
      if (fixedRowHeight) {
        average = height;
        oldAverage = oldHeight;
      } else {
        oldAverage = average = me.averageRowHeight;
        me.clearKnownHeights();
        average *= height / oldHeight;
      }
      me.calculateRowCount(false, true, true);
      me.topRow.setTop(me.topRow.dataIndex * (average + grid._rowBorderHeight), true);
      me.refresh();
      const newY = oldY * (average / oldAverage);
      if (newY !== oldY) {
        grid.scrollRowIntoView(topRow.id, {
          ...grid.defaultScrollOptions,
          block: "start",
          edgeOffset
        });
      }
    }
    me.trigger("rowHeight", { height, oldHeight });
  }
  /**
   * Get actually used row height, which includes any border and might be an average if using variable row height.
   * @readonly
   * @property {Number}
   */
  get rowOffsetHeight() {
    return Math.floor(this.preciseRowOffsetHeight);
  }
  get preciseRowOffsetHeight() {
    return (this.averageRowHeight || this._rowHeight) + this.grid._rowBorderHeight;
  }
  get minRowOffsetHeight() {
    return (this.minRowHeight || this._rowHeight) + this.grid._rowBorderHeight;
  }
  /*
  * How store CRUD affects the height map:
  *
  * | Operation | Result                            |
  * |-----------|-----------------------------------|
  * | add       | No. Appears on render             |
  * | insert    | No. Appears on render             |
  * | remove    | Remove entry                      |
  * | removeAll | Clear                             |
  * | update    | No                                |
  * | replace   | Height might differ, remove entry |
  * | move      | No                                |
  * | filter    | No                                |
  * | sort      | No                                |
  * | group     | No                                |
  * | dataset   | Clear                             |
  *
  * The above is handled in GridBase
  */
  /**
   * Returns `true` if all rows have a known height. They do if all rows are visited, or if RowManager is configured
   * with `fixedRowHeight`. If so, all tops can be calculated exactly, no guessing needed
   * @property {Boolean}
   * @private
   */
  get allHeightsKnown() {
    return this.fixedRowHeight || this.heightMap.size >= this.storeCount;
  }
  /**
   * Store supplied `height` using `id` as key in the height map. Called by `Row` when it gets its height.
   * Keeps `averageRowHeight` and `totalKnownHeight` up to date. Ignored when configured with `fixedRowHeight`
   * @param {String|Number} id
   * @param {Number} height
   * @internal
   */
  storeKnownHeight(id2, height) {
    const me = this, { heightMap } = me;
    if (!me.fixedRowHeight) {
      if (heightMap.has(id2)) {
        me.totalKnownHeight -= heightMap.get(id2);
      }
      heightMap.set(id2, height);
      me.totalKnownHeight += height;
      if (height < me.minRowHeight) {
        me.minRowHeight = height;
      }
      me.averageRowHeight = me.totalKnownHeight / heightMap.size;
    }
  }
  /**
   * Get the known or estimated offset height for the specified record id
   * @param {Core.data.Model} record
   * @returns {Number}
   * @private
   */
  getOffsetHeight(record) {
    const me = this;
    return (record && me.heightMap.get(record.id) || record && me.grid.getRowHeight(record) || me.averageRowHeight || me.rowHeight) + me.grid._rowBorderHeight;
  }
  /**
   * Invalidate cached height for a record. Removing it from `totalKnownHeight` and factoring it out of
   * `averageRowHeight`.
   * @param {Core.data.Model|Core.data.Model[]} records
   */
  invalidateKnownHeight(records) {
    const me = this;
    if (!me.fixedRowHeight) {
      const { heightMap } = me;
      records = ArrayHelper.asArray(records);
      records.forEach((record) => {
        if (record) {
          if (heightMap.has(record.id)) {
            me.totalKnownHeight -= heightMap.get(record.id);
            heightMap.delete(record.id);
          }
        }
      });
      me.averageRowHeight = me.totalKnownHeight / heightMap.size;
    }
  }
  /**
   * Invalidates all cached height and resets `averageRowHeight` and `totalKnownHeight`
   */
  clearKnownHeights() {
    this.heightMap.clear();
    this.averageRowHeight = this.totalKnownHeight = 0;
  }
  /**
   * Calculates a row top from its data index. Uses known values from the height map, unknown are substituted with
   * the average row height. When configured with `fixedRowHeight`, it will always calculate a correct value
   * @param {Number} index Index in store
   * @private
   */
  calculateTop(index) {
    if (this.fixedRowHeight) {
      return index * this.rowOffsetHeight;
    }
    const { storage } = this.store;
    let top = 0;
    for (let i = 0; i < index; i++) {
      const record = storage.getAt(i);
      top += this.getOffsetHeight(record);
    }
    return Math.floor(top);
  }
  //endregion
  //region Calculations
  /**
   * Returns top and bottom for rendered row or estimated coordinates for unrendered.
   * @param {Core.data.Model|String|Number} recordOrId Record or record id
   * @param {Boolean} [local] Pass true to get relative record coordinates
   * @param {Boolean} [roughly] Pass true to allow a less exact but cheaper estimate
   * @returns {Core.helper.util.Rectangle} Record bounds with format { x, y, width, height, bottom, right }
   * @category Calculations
   */
  getRecordCoords(recordOrId, local = false, roughly = false) {
    const me = this, row = me.getRowById(recordOrId);
    let scrollingViewport = me.client._bodyRectangle;
    if (!local) {
      scrollingViewport = me.client.refreshBodyRectangle();
    }
    if (row) {
      return new Rectangle(
        scrollingViewport.x,
        local ? Math.round(row.top) : Math.round(row.top + scrollingViewport.y - me.client.scrollable.y),
        Math.max(scrollingViewport.width, 0),
        // Width might go negative on very narrow screen
        row.offsetHeight
      );
    }
    const index = me.store.indexOf(recordOrId);
    return index !== -1 ? me.getRecordCoordsByIndex(index, local, roughly) : null;
  }
  /**
   * Returns top and bottom coordinates for specified row. If all heights are known, by for example using fixed row
   * height, the top is calculated exactly. If not, the top is estimated based on the closest known row.
   *
   * @param {Number} recordIndex Record index
   * @param {Boolean} [local]
   * @returns {Core.helper.util.Rectangle} Estimated record bounds
   * @category Calculations
   */
  getRecordCoordsByIndex(recordIndex, local = false, roughly = false) {
    var _a5;
    const me = this, { topRow, bottomRow } = me, scrollingViewport = me.client._bodyRectangle, { id: id2 } = me.store.getAt(recordIndex), height = me.preciseRowOffsetHeight, currentTopIndex = topRow.dataIndex, currentBottomIndex = bottomRow.dataIndex, maybeKnownHeight = Math.floor((_a5 = me.heightMap.get(id2)) != null ? _a5 : height);
    let recordY;
    if (me.allHeightsKnown && !roughly) {
      const top = me.calculateTop(recordIndex);
      recordY = local ? top : top + scrollingViewport.y - me.client.scrollable.y;
    } else {
      const calculateFrom = (
        // bottomRow is closest, calculate from it
        recordIndex > currentBottomIndex ? { index: recordIndex - currentBottomIndex - 1, y: bottomRow.bottom, from: "bottomRow" } : recordIndex > currentTopIndex / 2 ? { index: recordIndex - currentTopIndex, y: topRow.top, from: "topRow" } : { index: recordIndex, y: 0, from: "top" }
      ), top = Math.floor(calculateFrom.y + calculateFrom.index * height);
      recordY = local ? calculateFrom.from === "topRow" ? top + height - maybeKnownHeight : top : top + scrollingViewport.y - me.client.scrollable.y;
    }
    const result = new Rectangle(scrollingViewport.x, recordY, scrollingViewport.width, maybeKnownHeight);
    result.virtual = true;
    result.block = result.bottom < scrollingViewport.y ? "start" : result.y > scrollingViewport.bottom ? "end" : "nearest";
    return result;
  }
  /**
   * Total estimated grid height (used for scroller)
   * @property {Number}
   * @readonly
   * @category Calculations
   */
  get totalHeight() {
    return this._totalHeight;
  }
  //endregion
  //region Iteration etc.
  /**
   * Calls a function for each Row
   * @param {Function} fn Function that will be called with Row as first parameter
   * @category Iteration
   */
  forEach(fn2) {
    this.rows.forEach(fn2);
  }
  /**
   * Iterator that allows you to do for (let row of rowManager)
   * @category Iteration
   */
  [Symbol.iterator]() {
    return this.rows[Symbol.iterator]();
  }
  //endregion
  //region Scrolling & rendering
  /**
   * Refresh a single cell.
   * @param {Core.data.Model} record Record for row holding the cell that should be updated
   * @param {String|Number} columnId Column id to identify the cell within the row
   * @returns {Boolean} Returns `true` if cell was found and refreshed, `false` if not
   */
  refreshCell(record, columnId) {
    const cellContext = new GridLocation({ grid: this.grid, record, columnId });
    return Boolean(cellContext.cell && cellContext.row.renderCell(cellContext));
  }
  /**
   * Renders from the top of the grid, also resetting scroll to top. Used for example when collapsing all groups.
   * @category Scrolling & rendering
   */
  returnToTop() {
    const me = this;
    me.topIndex = 0;
    me.lastScrollTop = 0;
    if (me.topRow) {
      me.topRow.dataIndex = 0;
      me.topRow.setTop(0, true);
    }
    me.refresh();
    me.grid.scrollable.y = 0;
  }
  /**
   * Renders from specified records row and down (used for example when collapsing a group, does not affect rows above).
   * @param {Core.data.Model} record Record of first row to render
   * @category Scrolling & rendering
   */
  renderFromRecord(record) {
    const row = this.getRowById(record.id);
    if (row) {
      this.renderFromRow(row);
    }
  }
  /**
   * Renders from specified row and down (used for example when collapsing a group, does not affect rows above).
   * @param {Grid.row.Row} fromRow First row to render
   * @category Scrolling & rendering
   */
  renderFromRow(fromRow = null, animatingRowPosition) {
    const me = this, {
      rows,
      store,
      topIndex,
      topRow,
      storeCount
    } = me;
    let fromRowIndex = fromRow ? rows.indexOf(fromRow) : 0;
    if (me.calculateRowCount(false, storeCount < rows.length && !animatingRowPosition, true) === 0 || fromRowIndex >= rows.length) {
      me.estimateTotalHeight(true);
      return;
    }
    if (me.topIndex < topIndex) {
      me.topRow.setTop(me.calculateTop(me.topIndex), true);
      fromRowIndex = 0;
      fromRow = me.topRow;
    } else if (fromRow && rows.indexOf(fromRow) < 0) {
      fromRow = rows[fromRowIndex] || topRow;
    }
    let dataIndex = fromRow ? fromRow.dataIndex : rows[0].dataIndex;
    const recordsAfter = storeCount - dataIndex - 1, toRowIndex = Math.min(rows.length - 1, fromRowIndex + recordsAfter);
    let leftOverCount = rows.length - toRowIndex - 1, top = fromRowIndex > 0 ? rows[fromRowIndex - 1].bottom : rows[fromRowIndex].top, row;
    const newTops = [];
    for (let i = fromRowIndex; i <= toRowIndex; i++) {
      row = rows[i];
      row.dataIndex = dataIndex;
      if (animatingRowPosition) {
        newTops[i] = top;
      } else {
        row.setTop(top, true);
      }
      row.render(dataIndex, store.getAt(dataIndex++), false);
      top += row.offsetHeight;
    }
    if (animatingRowPosition) {
      rows[fromRowIndex].element.getBoundingClientRect();
      for (let i = fromRowIndex; i <= toRowIndex; i++) {
        rows[i].setTop(newTops[i], true);
      }
    }
    while (leftOverCount-- > 0) {
      me.displayRecordAtTop();
    }
    if (me.bottomRow.bottom < me.viewHeight + me.lastScrollTop) {
      me.calculateRowCount();
    }
    me.estimateTotalHeight(true);
    me.trigger("renderDone");
  }
  /**
   * Renders the passed array (or [Set](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set)) of {@link Grid.row.Row rows}
   * @param {Grid.row.Row[]|Set} rows The rows to render
   * @category Scrolling & rendering
   */
  renderRows(rows) {
    let oldHeight, heightChanged = false;
    rows = Array.from(rows);
    rows.sort((a, b) => a.dataIndex - b.dataIndex);
    for (const row of rows) {
      oldHeight = row.height;
      row.render(null, null, false);
      heightChanged |= row.height !== oldHeight;
    }
    if (heightChanged) {
      this.translateFromRow(rows[0]);
    }
    this.trigger("renderDone");
  }
  bufferedTranslateFromRow() {
    this.translateFromRow(this.topRow);
  }
  /**
   * Translates all rows after the specified row. Used when a single rows height is changed and the others should
   * rearrange. (Called from Row#render)
   * @param {Grid.row.Row} fromRow
   * @private
   * @category Scrolling & rendering
   */
  translateFromRow(fromRow, batch = false) {
    const me = this;
    let top = fromRow.bottom, row, index;
    for (index = fromRow.dataIndex + 1, row = me.getRow(index); row; row = me.getRow(++index)) {
      top = row.translate(top);
    }
    if (!batch) {
      me.estimateTotalHeight(true);
    }
  }
  /**
   * Rerender all rows
   * @category Scrolling & rendering
   * @privateparam {Boolean} force Force re-rendering even if there are no rows currently
   */
  refresh(force = false) {
    const me = this, { topRow } = me;
    if (!topRow && !force || me.grid.refreshSuspended) {
      return;
    }
    me.idMap = {};
    me.renderFromRow(topRow);
    me.trigger("refresh");
  }
  /**
   * Makes sure that specified record is displayed in view
   * @param newScrollTop Top of visible section
   * @param [forceRecordIndex] Index of record to display at center
   * @private
   * @category Scrolling & rendering
   */
  jumpToPosition(newScrollTop, forceRecordIndex) {
    const me = this, { store, heightMap, storeCount } = me;
    if (me.allHeightsKnown && !me.fixedRowHeight) {
      const top = newScrollTop - me.prependBufferHeight, border = me.grid._rowBorderHeight;
      let accumulated = 0, targetIndex = 0;
      while (accumulated < top) {
        const record = store.records[targetIndex];
        accumulated += heightMap.get(record.id) + border;
        targetIndex++;
      }
      const startIndex = Math.max(Math.min(targetIndex, storeCount - me.rowCount), 0);
      me.lastScrollTop = newScrollTop;
      me.topRow.dataIndex = me.topIndex = startIndex;
      me.topRow.setTop(me.calculateTop(startIndex), false);
      me.refresh();
    } else {
      const rowHeight = me.preciseRowOffsetHeight, targetIndex = forceRecordIndex == null ? Math.floor(newScrollTop / rowHeight) - me.prependRowBuffer : forceRecordIndex - Math.floor(me.rowCount / 2), startIndex = Math.max(Math.min(targetIndex, storeCount - me.rowCount), 0), viewportTop = me.client.scrollable.y, viewportBottom = Math.min(me.client._bodyRectangle.height + viewportTop + me.appendBufferHeight, me.totalHeight);
      me.lastScrollTop = newScrollTop;
      me.topRow.dataIndex = me.topIndex = startIndex;
      me.topRow.setTop(Math.floor(startIndex * rowHeight), false);
      me.refresh();
      if (me.bottomRow.bottom < viewportBottom) {
        me.calculateRowCount(false, false, false);
        while (me.bottomRow.bottom < viewportBottom && me._rows[me.prependRowBuffer].top < viewportTop && me.bottomRow.dataIndex < storeCount - 1) {
          me.displayRecordAtBottom();
        }
      }
      me.estimateTotalHeight();
    }
    if (forceRecordIndex != null) {
      const { scrollable } = me.grid, targetRow = me.getRow(forceRecordIndex), rowCenter = targetRow && Rectangle.from(targetRow._elementsArray[0]).center.y, viewportCenter = scrollable.viewport.center.y;
      if (targetRow) {
        scrollable.y = newScrollTop = Math.floor(scrollable.y + (rowCenter - viewportCenter));
      }
    }
    return newScrollTop;
  }
  /**
   * Jumps to a position if it is far enough from current position. Otherwise does nothing.
   * @private
   * @category Scrolling & rendering
   */
  warpIfNeeded(newScrollTop) {
    const me = this, result = { newScrollTop, deltaTop: newScrollTop - me.lastScrollTop };
    if (Math.abs(result.deltaTop) > me.rowCount * me.rowOffsetHeight * 3) {
      let index;
      if (me.scrollTargetRecordId) {
        index = me.store.indexOf(me.scrollTargetRecordId);
      }
      me.grid.onFocusedRowDerender();
      result.newScrollTop = me.jumpToPosition(newScrollTop, index);
      result.deltaTop = 0;
    }
    return result;
  }
  /**
   * Handles virtual rendering (only visible rows + buffer are in dom) for rows
   * @param {Number} newScrollTop The `Y` scroll position for which to render rows.
   * @param {Boolean} [force=false] Pass `true` to update the rendered row block even if the scroll position has not changed.
   * @returns {Number} Adjusted height required to fit rows
   * @private
   * @category Scrolling & rendering
   */
  updateRenderedRows(newScrollTop, force, ignoreError = false) {
    const me = this;
    if (me.rowCount === 0) {
      return 0;
    }
    let result = me.totalHeight;
    if (force || // Only react if we have scrolled by one row or more
    Math.abs(newScrollTop - me.lastScrollTop) >= me.rowOffsetHeight || // or if we have a gap at top/bottom (#9375)
    me.topRow.top > newScrollTop || me.bottomRow.bottom < newScrollTop + me.viewHeight) {
      const posInfo = me.warpIfNeeded(newScrollTop);
      me.scrollTargetRecordId = null;
      me.lastScrollTop = posInfo.newScrollTop;
      if (posInfo.deltaTop > 0) {
        me.fillBelow(posInfo.newScrollTop);
      } else if (posInfo.deltaTop < 0) {
        me.fillAbove(posInfo.newScrollTop);
      }
      if (!me.fixedRowHeight && !ignoreError) {
        me.correctError(posInfo, newScrollTop);
      }
      result = me.estimateTotalHeight();
    }
    return result;
  }
  correctError(posInfo, newScrollTop) {
    const me = this;
    let error = 0;
    if (me.allHeightsKnown) {
      error = me.topRow.top - me.calculateTop(me.topRow.dataIndex);
    } else {
      if (
        // Scrolling up within top zone
        posInfo.deltaTop < 0 && newScrollTop < me.viewHeight * 2 || // Scrolling down within bottom zone
        posInfo.deltaTop > 0 && newScrollTop > me.totalHeight - me.viewHeight * 2 - 3
      ) {
        error = me.topRow.top - me.calculateTop(me.topRow.dataIndex);
      }
    }
    if (error) {
      me.offsetRows(-error);
      me.grid.scrollable.y = me.lastScrollTop = me.grid.scrollable.y - error;
    }
  }
  /**
   * Moves as many rows from the bottom to the top that are needed to fill to current scroll pos.
   * @param newTop Scroll position
   * @private
   * @category Scrolling & rendering
   */
  fillAbove(newTop) {
    const me = this, { rows } = me, prependIndex = Math.min(me.prependRowBuffer, rows.length - 1);
    while (
      // Prepend buffer should be above viewport
      rows[prependIndex].top > newTop && // unless we have reached the top
      me.topIndex > 0
    ) {
      me.displayRecordAtTop();
    }
    me.trigger("renderDone");
  }
  /**
   * Moves as many rows from the top to the bottom that are needed to fill to current scroll pos.
   * @param newTop Scroll position
   * @private
   * @category Scrolling & rendering
   */
  fillBelow(newTop) {
    const me = this, { rows, storeCount } = me, { length } = rows, appendIndex = Math.min(length - Math.min(me.appendRowBuffer, length - 1), length - 1), newBottom = newTop + me.viewHeight;
    while (
      // Append buffer should be below viewport
      rows[appendIndex].top < newBottom && // unless we have reached the bottom
      me.bottomRow.dataIndex < storeCount - 1
    ) {
      me.displayRecordAtBottom();
    }
    me.trigger("renderDone");
  }
  /**
   * Estimates height needed to fit all rows, based on average row height. Also offsets rows if needed to not be above
   * the reachable area of the view.
   * @param {Boolean} [immediate] Specify true to pass the `immediate` flag on to any listeners (probably only Grid
   * cares. Used to bypass buffered element resize)
   * @returns {Number}
   * @private
   * @category Scrolling & rendering
   */
  estimateTotalHeight(immediate = false) {
    const me = this;
    if (me.grid.renderingRows) {
      return;
    }
    const { bottomRow, storeCount } = me, unknownCount = storeCount - me.heightMap.size;
    let estimate;
    if (me.fixedRowHeight) {
      estimate = storeCount * me.rowOffsetHeight;
    } else {
      estimate = // Known height, from entries in heightMap
      me.totalKnownHeight + // Those heights are "clientHeights", estimate needs to include borders
      me.heightMap.size * me.grid._rowBorderHeight + // Add estimate for rows with unknown height
      unknownCount * me.preciseRowOffsetHeight;
      if (bottomRow && unknownCount) {
        const bottom = bottomRow.bottom;
        if (bottom > estimate || me.topIndex + me.rowCount >= storeCount && estimate > bottom && bottom > 0) {
          estimate = bottom;
          if (bottomRow.dataIndex < storeCount - 1) {
            estimate += (storeCount - 1 - bottomRow.dataIndex) * me.preciseRowOffsetHeight;
          }
        }
      }
      estimate = Math.floor(estimate);
    }
    if (estimate !== me.totalHeight) {
      if (me.trigger("changeTotalHeight", { totalHeight: estimate, immediate }) !== false) {
        me._totalHeight = estimate;
      }
    }
    return estimate;
  }
  /**
   * Moves a row from bottom to top and renders the corresponding record to it.
   * @returns {Number} New row height
   * @private
   * @category Scrolling & rendering
   */
  displayRecordAtTop() {
    var _a5;
    const me = this, { grid } = me, recordIndex = me.topIndex - 1, record = me.store.getAt(recordIndex), bottomRow = me.bottomRow, bottomRowTop = bottomRow.top;
    me.trigger("beforeTranslateRow", {
      row: bottomRow,
      newRecord: record
    });
    if (bottomRow.dataIndex === ((_a5 = grid.focusedCell) == null ? void 0 : _a5.rowIndex)) {
      grid.onFocusedRowDerender();
    }
    bottomRow._top = me.topRow.top - me.getOffsetHeight(record);
    bottomRow.estimatedTop = !me.fixedRowHeight;
    bottomRow.render(recordIndex, record, false);
    bottomRow._top = bottomRowTop;
    bottomRow.setBottom(me.topRow.top);
    bottomRow.estimatedTop = false;
    me.topIndex--;
    me._rows.unshift(me._rows.pop());
    me.fixIndices();
    return bottomRow.offsetHeight;
  }
  /**
   * Moves a row from top to bottom and renders the corresponding record to it.
   * @returns {Number} New row height
   * @private
   * @category Scrolling & rendering
   */
  displayRecordAtBottom() {
    var _a5;
    const me = this, { grid } = me, recordIndex = me.topIndex + me.rowCount, record = me.store.getAt(recordIndex), topRow = me.topRow;
    me.trigger("beforeTranslateRow", {
      row: topRow,
      newRecord: record
    });
    if (topRow.dataIndex === ((_a5 = grid.focusedCell) == null ? void 0 : _a5.rowIndex)) {
      grid.onFocusedRowDerender();
    }
    topRow.dataIndex = recordIndex;
    topRow.setTop(me.bottomRow.bottom);
    topRow.render(recordIndex, record, false);
    me.topIndex++;
    me._rows.push(me._rows.shift());
    me.fixIndices();
    return topRow.offsetHeight;
  }
  fixIndices() {
    for (let i = 0, { rows } = this, { length } = rows; i < length; i++) {
      rows[i].index = i;
    }
  }
  //endregion
};
__publicField(RowManager, "delayable", {
  bufferedTranslateFromRow: "raf"
});
RowManager.featureClass = "";
RowManager._$name = "RowManager";

// ../Grid/lib/Grid/util/GridScroller.js
var xAxis2 = {
  x: 1
};
var subGridFilter = (w) => w.isSubGrid;
var GridScroller = class extends Scroller {
  addScroller(scroller) {
    (this.xScrollers || (this.xScrollers = [])).push(scroller);
  }
  addPartner(otherScroller, axes = xAxis2, syncScrollbar = true) {
    if (typeof axes === "string") {
      axes = {
        [axes]: 1
      };
    }
    if (axes.x) {
      otherScroller.owner.initScroll();
      const subGrids = this.widget.items.filter(subGridFilter), otherSubGrids = otherScroller.widget.items.filter(subGridFilter);
      for (let i = 0, { length } = subGrids; i < length; i++) {
        subGrids[i].scrollable.addPartner(otherSubGrids[i].scrollable, "x");
      }
    }
    if (syncScrollbar && DomHelper.scrollBarWidth && otherScroller.isGridScroller) {
      otherScroller.ion({
        overflowChange: "onPartnerOverflowChange",
        thisObj: this
      });
      this.onPartnerOverflowChange({
        source: otherScroller,
        y: otherScroller.hasOverflowY
      });
    }
    super.addPartner(otherScroller, { ...axes, x: false });
  }
  /**
   * Called when a partner GridScroller that we are sharing X position with changes its overflowing state.
   *
   * This will mean the addition or removal of a vertical scrollbar.
   *
   * All partners must stay in sync. If another parter has a vertical scrollbar
   * and we do not, we must set our overflowY to 'scroll' so that we show an empty
   * scrollbar to keep widths synchronized.
   * @param {Object} event A {@link Core.helper.util.Scroller#event-overflowChange} event
   * @internal
   */
  onPartnerOverflowChange({ source: otherScrollable, y }) {
    const me = this, ourY = me.hasOverflowY, otherY = y;
    if (ourY && !otherY) {
      otherScrollable.overflowY = "scroll";
    }
    if (!ourY && otherY) {
      me.overflowY = "scroll";
    } else if (!ourY && !otherY) {
      me.overflowY = otherScrollable.overflowY = true;
    }
    me.owner.refreshVirtualScrollbars();
  }
  removePartner(otherScroller) {
    if (DomHelper.scrollBarWidth && otherScroller.isGridScroller) {
      otherScroller.un({
        overflowChange: "onPartnerOverflowChange",
        thisObj: this
      });
    }
    this.xScrollers.forEach((scroller, i) => {
      if (!scroller.isDestroyed) {
        scroller.removePartner(otherScroller.xScrollers[i]);
      }
    });
    super.removePartner(otherScroller);
  }
  updateOverflowX(overflowX) {
    var _a5;
    const hideScroll = overflowX === false;
    (_a5 = this.xScrollers) == null ? void 0 : _a5.forEach((s) => s.overflowX = hideScroll ? "hidden" : "hidden-scroll");
    this.widget.virtualScrollers.classList.toggle("b-hide-display", hideScroll);
  }
  scrollIntoView(element, options) {
    if (element.nodeType === Element.ELEMENT_NODE && this.element.contains(element)) {
      for (const subGridScroller of this.xScrollers) {
        if (subGridScroller.element.contains(element)) {
          return subGridScroller.scrollIntoView(element, options);
        }
      }
    } else {
      return super.scrollIntoView(element, { ...options, x: false });
    }
  }
  hasOverflow(axis = "y") {
    return axis === "y" ? this.scrollHeight > this.clientHeight : false;
  }
  set x(x) {
    if (this.xScrollers) {
      this.xScrollers[0].x = x;
    }
  }
  get x() {
    return this.xScrollers ? this.xScrollers[0].x : 0;
  }
};
__publicField(GridScroller, "$name", "GridScroller");
GridScroller._$name = "GridScroller";

// ../Grid/lib/Grid/view/Header.js
var Header = class extends Bar {
  get subGrid() {
    return this._subGrid;
  }
  set subGrid(subGrid) {
    this._subGrid = this.owner = subGrid;
  }
  get region() {
    var _a5;
    return (_a5 = this.subGrid) == null ? void 0 : _a5.region;
  }
  changeElement(element, was) {
    const { region } = this;
    this.getConfig("columns");
    return super.changeElement({
      className: {
        "b-grid-header-scroller": 1,
        [`b-grid-header-scroller-${region}`]: region
      },
      role: null,
      children: [{
        reference: "headersElement",
        className: {
          "b-grid-headers": 1,
          [`b-grid-headers-${region}`]: region
        },
        dataset: {
          region,
          reference: "headersElement",
          maxDepth: this.maxDepth
        }
      }]
    }, was);
  }
  get overflowElement() {
    return this.headersElement;
  }
  /**
   * Recursive column header config creator.
   * Style not included because of CSP. Widths are fixed up in
   * {@link #function-fixHeaderWidths}
   * @private
   */
  getColumnConfig(column) {
    var _a5, _b2;
    const {
      id: id2,
      align,
      resizable,
      isLeaf,
      isParent,
      isLastInSubGrid,
      cls,
      childLevel,
      field,
      tooltip,
      children,
      isFocusable: isFocusable2,
      grid
    } = column, focusedCell = grid == null ? void 0 : grid.focusedCell, isFocused = (focusedCell == null ? void 0 : focusedCell.rowIndex) === -1 && (focusedCell == null ? void 0 : focusedCell.column) === column, style = {};
    if (column.isVisible) {
      if (column.flex) {
        style.flex = column.flex;
      } else if (column.width) {
        style.width = DomHelper.setLength(column.width);
      }
      return {
        style,
        className: {
          "b-grid-header": 1,
          "b-grid-header-parent": isParent,
          [`b-level-${childLevel}`]: 1,
          [`b-depth-${(_a5 = column.meta.depth) != null ? _a5 : 0}`]: 1,
          [`b-grid-header-align-${align}`]: align,
          "b-grid-header-resizable": resizable && isLeaf,
          [cls]: cls,
          "b-collapsible": column.collapsible,
          "b-last-parent": isParent && isLastInSubGrid,
          "b-last-leaf": isLeaf && isLastInSubGrid,
          "b-header-widgets": (_b2 = column.headerWidgets) == null ? void 0 : _b2.length
        },
        role: isFocusable2 ? "columnheader" : null,
        "aria-sort": "none",
        "aria-label": column.ariaLabel,
        [isFocusable2 ? "tabIndex" : ""]: isFocused ? 0 : -1,
        dataset: {
          ...Tooltip.encodeConfig(tooltip),
          columnId: id2,
          [field ? "column" : ""]: field
        },
        children: [
          {
            className: "b-grid-header-text",
            role: null,
            children: [{
              [grid && isFocusable2 ? "id" : ""]: `${grid == null ? void 0 : grid.id}-column-${column.id}`,
              className: {
                "b-grid-header-text-content": 1,
                "b-has-text": Boolean(column.text)
              }
            }]
          },
          children ? {
            className: "b-grid-header-children",
            children: children.map((child) => this.getColumnConfig(child)),
            syncOptions: {
              syncIdField: "columnId"
            }
          } : null,
          {
            className: "b-grid-header-resize-handle"
          }
        ]
      };
    }
  }
  // used by safari to fix flex when rows width shrink below this value
  calculateMinWidthForSafari() {
    let minWidth = 0;
    this.columns.visibleColumns.forEach((column) => {
      minWidth += column.calculateMinWidth();
    });
    return minWidth;
  }
  /**
   * Fix header widths (flex or fixed width) after rendering. Not a part of template any longer because of CSP
   * @private
   */
  fixHeaderWidths() {
    this.fixCellWidths();
  }
  refreshHeaders() {
    const me = this;
    me.columns.traverse((column) => me.refreshColumn(column));
    me.fixHeaderWidths();
  }
  refreshColumn(column) {
    const me = this, headerElement = me.getBarCellElement(column.id);
    if (headerElement) {
      let html = column.headerText;
      if (column.headerRenderer) {
        html = column.headerRenderer.call(column.thisObj || me, { column, headerElement });
        if (DomHelper.isReactElement(html)) {
          return DomHelper.handleReactHeaderElement(column, headerElement, html);
        }
      }
      if (column.headerWidgetMap) {
        Object.values(column.headerWidgetMap).forEach((widget) => {
          widget.render(column.textWrapper);
        });
      }
      if (column.icon) {
        html = `<i class="${StringHelper.encodeHtml(column.icon)}"></i>` + (html || "");
      }
      const innerEl = headerElement.querySelector(".b-grid-header-text-content");
      if (innerEl) {
        innerEl.innerHTML = html || "";
      }
    }
  }
  get columns() {
    const me = this, result = super.columns;
    if (!me.columnsDetacher) {
      me.columnsDetacher = result.ion({
        change() {
          me.initDepths();
        },
        thisObj: me
      });
      me.initDepths();
    }
    return result;
  }
  set columns(columns) {
    super.columns = columns;
  }
  /**
   * Depths are used for styling of grouped headers. Sets them on meta.
   * @private
   */
  initDepths(columns = this.columns.topColumns, parent = null) {
    const me = this;
    let maxDepth = 0;
    if (parent == null ? void 0 : parent.meta) {
      parent.meta.depth++;
    }
    for (const column of columns) {
      const { meta } = column;
      meta.depth = 0;
      if (column.children) {
        me.initDepths(column.children.filter(me.columns.chainedFilterFn), column);
        if (meta.depth && parent) {
          parent.meta.depth += meta.depth;
        }
      }
      if (meta.depth > maxDepth) {
        maxDepth = meta.depth;
      }
    }
    if (!parent) {
      me.maxDepth = maxDepth;
    }
    return maxDepth;
  }
  //endregion
  //region Getters
  /**
   * Get the header cell element for the specified column.
   * @param {String} columnId Column id
   * @returns {HTMLElement} Header cell element
   */
  getHeader(columnId) {
    return this.getBarCellElement(columnId);
  }
  //endregion
  get contentElement() {
    return this.element.firstElementChild;
  }
  refreshContent() {
    const me = this;
    DomSync.sync({
      domConfig: {
        children: me.columns.topColumns.map((col) => me.getColumnConfig(col)),
        onlyChildren: true
      },
      targetElement: me.contentElement,
      strict: true,
      syncIdField: "columnId",
      releaseThreshold: 0
    });
    me.refreshHeaders();
  }
  onInternalPaint({ firstPaint }) {
    if (firstPaint) {
      this.refreshContent();
    }
  }
};
__publicField(Header, "$name", "Header");
__publicField(Header, "type", "gridheader");
Header.initClass();
Header._$name = "Header";

// ../Grid/lib/Grid/view/mixin/GridElementEvents.js
var gridBodyElementEventHandlers = {
  touchstart: "onElementTouchStart",
  touchmove: "onElementTouchMove",
  touchend: "onElementTouchEnd",
  pointerover: "onElementMouseOver",
  mouseout: "onElementMouseOut",
  mousedown: "onElementMouseDown",
  mousemove: "onElementMouseMove",
  mouseup: "onElementMouseUp",
  click: "onHandleElementClick",
  dblclick: "onElementDblClick",
  keyup: "onElementKeyUp",
  keypress: "onElementKeyPress",
  contextmenu: "onElementContextMenu",
  pointerdown: "onElementPointerDown",
  pointerup: "onElementPointerUp"
};
var eventProps2 = [
  "pageX",
  "pageY",
  "clientX",
  "clientY",
  "screenX",
  "screenY"
];
var textKeys = {
  ArrowUp: 1,
  ArrowDown: 1,
  ArrowLeft: 1,
  ArrowRight: 1,
  Backspace: 1,
  Delete: 1
};
function toggleHover(element, add = true) {
  element == null ? void 0 : element.classList.toggle("b-hover", add);
}
function setCellHover(columnId, row, add = true) {
  row && columnId && toggleHover(row.getCell(columnId), add);
}
var GridElementEvents_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    //region Config
    static get configurable() {
      return {
        /**
         * The currently hovered grid cell
         * @member {HTMLElement}
         * @readonly
         * @category Misc
         */
        hoveredCell: null,
        /**
         * Time in ms until a longpress is triggered
         * @prp {Number}
         * @default
         * @category Misc
         */
        longPressTime: 400,
        /**
         * Set to `true` to listen for CTRL-Z (CMD-Z on Mac OS) keyboard event and trigger undo (redo when SHIFT is
         * pressed). Only applicable when using a {@link Core.data.stm.StateTrackingManager}.
         * @prp {Boolean}
         * @default
         * @category Misc
         */
        enableUndoRedoKeys: true,
        /**
         * A CSS class to add to hovered row elements
         * @config {String}
         * @default
         * @category Misc
         */
        hoverCls: "b-hover",
        keyMap: {
          "Ctrl+z": "undoRedoKeyPress",
          "Ctrl+Shift+z": "undoRedoKeyPress",
          " ": { handler: "clickCellByKey", weight: 1e3 }
        }
      };
    }
    //endregion
    //region Events
    /**
     * Fired when user clicks in a grid cell
     * @event cellClick
     * @param {Grid.view.Grid} grid The grid instance
     * @param {Core.data.Model} record The record representing the row
     * @param {Grid.column.Column} column The column to which the cell belongs
     * @param {HTMLElement} cellElement The cell HTML element
     * @param {HTMLElement} target The target element
     * @param {MouseEvent} event The native DOM event
     */
    /**
     * Fired when user double clicks a grid cell
     * @event cellDblClick
     * @param {Grid.view.Grid} grid The grid instance
     * @param {Core.data.Model} record The record representing the row
     * @param {Grid.column.Column} column The column to which the cell belongs
     * @param {HTMLElement} cellElement The cell HTML element
     * @param {HTMLElement} target The target element
     * @param {MouseEvent} event The native DOM event
     */
    /**
     * Fired when user activates contextmenu in a grid cell
     * @event cellContextMenu
     * @param {Grid.view.Grid} grid The grid instance
     * @param {Core.data.Model} record The record representing the row
     * @param {Grid.column.Column} column The column to which the cell belongs
     * @param {HTMLElement} cellElement The cell HTML element
     * @param {HTMLElement} target The target element
     * @param {MouseEvent} event The native DOM event
     */
    /**
     * Fired when user moves the mouse over a grid cell
     * @event cellMouseOver
     * @param {Grid.view.Grid} grid The grid instance
     * @param {Core.data.Model} record The record representing the row
     * @param {Grid.column.Column} column The column to which the cell belongs
     * @param {HTMLElement} cellElement The cell HTML element
     * @param {HTMLElement} target The target element
     * @param {MouseEvent} event The native DOM event
     */
    /**
     * Fired when a user moves the mouse out of a grid cell
     * @event cellMouseOut
     * @param {Grid.view.Grid} grid The grid instance
     * @param {Core.data.Model} record The record representing the row
     * @param {Grid.column.Column} column The column to which the cell belongs
     * @param {HTMLElement} cellElement The cell HTML element
     * @param {HTMLElement} target The target element
     * @param {MouseEvent} event The native DOM event
     */
    /**
     * Fired when the mouse enters a row
     * @event rowMouseEnter
     * @param {Grid.view.Grid} source The grid instance
     * @param {Core.data.Model} record The record representing the hovered row
     * @param {Grid.column.Column} column The column currently hovered
     * @param {HTMLElement} cellElement The cell HTML element
     * @param {MouseEvent} event The native DOM event
     */
    /**
     * Fired when the mouse leaves a row
     * @event rowMouseLeave
     * @param {Grid.view.Grid} source The grid instance
     * @param {Core.data.Model} record The record representing the row that the mouse left
     * @param {HTMLElement} cellElement The cell HTML element that the mouse left
     * @param {MouseEvent} event The native DOM event
     */
    /**
     * Fired when the mouse enters a cell
     * @event cellMouseEnter
     * @param {Grid.view.Grid} source The grid instance
     * @param {Core.data.Model} record The record representing the hovered cell
     * @param {Grid.column.Column} column The column currently hovered
     * @param {HTMLElement} cellElement The cell HTML element
     * @param {MouseEvent} event The native DOM event
     */
    /**
     * Fired when the mouse leaves a cell
     * @event cellMouseLeave
     * @param {Grid.view.Grid} source The grid instance
     * @param {Core.data.Model} record The record representing the cell that the mouse left
     * @param {Grid.column.Column} column The column representing the cell that the mouse left
     * @param {HTMLElement} cellElement The cell HTML element that the mouse left
     * @param {MouseEvent} event The native DOM event
     */
    //endregion
    //region Event handling
    /**
     * Init listeners for a bunch of dom events. All events are handled by handleEvent().
     * @private
     * @category Events
     */
    initInternalEvents() {
      const handledEvents = Object.keys(gridBodyElementEventHandlers), len = handledEvents.length, listeners = {
        element: this.bodyElement,
        thisObj: this
      };
      for (let i = 0; i < len; i++) {
        const eventName = handledEvents[i];
        listeners[eventName] = {
          handler: "handleEvent"
        };
        if (eventName.startsWith("touch")) {
          listeners[eventName].passive = false;
        }
      }
      EventHelper.on(listeners);
      EventHelper.on({
        mouseenter: "onGridBodyMouseEnterOrLeave",
        mouseleave: "onGridBodyMouseEnterOrLeave",
        focusin: "onGridBodyFocusIn",
        element: this.bodyElement,
        thisObj: this,
        capture: true
      });
    }
    /**
     * This method finds the cell location of the passed event. It returns an object describing the cell.
     * @param {Event} event A Mouse, Pointer or Touch event targeted at part of the grid.
     * @param {Boolean} [includeSingleAxisMatch] Set to `true` to return a cell from xy either above or below the Grid's
     * body or to the left or right.
     * @returns {Object} An object containing the following properties:
     * - `cellElement` - The cell element clicked on.
     * - `column` - The {@link Grid.column.Column column} clicked under.
     * - `columnId` - The `id` of the {@link Grid.column.Column column} clicked under.
     * - `record` - The {@link Core.data.Model record} clicked on.
     * - `id` - The `id` of the {@link Core.data.Model record} clicked on.
     * @internal
     * @category Events
     */
    getCellDataFromEvent(event, includeSingleAxisMatch = false) {
      var _a6, _b2;
      const me = this, { target } = event, targetIsRow = target.classList.contains("b-grid-row");
      let cellElement = target.closest(".b-grid-cell");
      if (!cellElement && includeSingleAxisMatch && !targetIsRow && !target.classList.contains("b-grid-subgrid")) {
        const { columns } = me, {
          top,
          left,
          right,
          bottom
        } = me.bodyContainer.getBoundingClientRect();
        let match, { x, y } = event;
        if (x >= left && x <= right) {
          y = match = Math.ceil(me[`${y < top ? "first" : "last"}FullyVisibleRow`].element.getBoundingClientRect().y);
        } else if (y >= top && y <= bottom) {
          x = match = Math.ceil(columns.visibleColumns[x < left ? 0 : columns.visibleColumns.length - 1].element.getBoundingClientRect().x);
        }
        if (match !== void 0) {
          cellElement = (_a6 = DomHelper.childFromPoint(target, event.offsetX, event.offsetY)) == null ? void 0 : _a6.closest(".b-grid-cell");
        }
      } else if (targetIsRow) {
        cellElement = (_b2 = DomHelper.childFromPoint(target, event.offsetX, event.offsetY - target.offsetHeight / 2)) == null ? void 0 : _b2.closest(".b-grid-cell");
      }
      if (cellElement) {
        return me.getCellDataFromElement(cellElement);
      }
    }
    /**
     * This method finds the cell location of the passed element. It returns an object describing the cell.
     * @param {HTMLElement} cellElement A cell element or any element inside a cell.
     * @returns {Object} An object containing the following properties:
     * - `cellElement` - The cell element clicked on.
     * - `column` - The {@link Grid.column.Column column} clicked under.
     * - `columnId` - The `id` of the {@link Grid.column.Column column} clicked under.
     * - `record` - The {@link Core.data.Model record} clicked on.
     * - `id` - The `id` of the {@link Core.data.Model record} clicked on.
     * @internal
     * @category Events
     */
    getCellDataFromElement(cellElement) {
      const me = this;
      if (cellElement = cellElement.closest(".b-grid-cell")) {
        const cellData = DomDataStore.get(cellElement), { id: id2, columnId } = cellData, record = me.store.getById(id2), column = me.columns.getById(columnId);
        return record ? {
          cellElement,
          cellData,
          columnId,
          id: id2,
          record,
          column,
          cellSelector: { id: id2, columnId }
        } : null;
      }
    }
    /**
     * This method finds the header location of the passed event. It returns an object describing the header.
     * @param {Event} event A Mouse, Pointer or Touch event targeted at part of the grid.
     * @returns {Object} An object containing the following properties:
     * - `headerElement` - The header element clicked on.
     * - `column` - The {@link Grid.column.Column column} clicked under.
     * - `columnId` - The `id` of the {@link Grid.column.Column column} clicked under.
     * @internal
     * @category Events
     */
    getHeaderDataFromEvent(event) {
      const headerElement = event.target.closest(".b-grid-header");
      if (headerElement) {
        const headerData = ObjectHelper.assign({}, headerElement.dataset), { columnId } = headerData, column = this.columns.getById(columnId);
        return column ? {
          headerElement,
          headerData,
          columnId,
          column
        } : null;
      }
    }
    /**
     * Handles all dom events, routing them to correct functions (touchstart -> onElementTouchStart)
     * @param event
     * @private
     * @category Events
     */
    handleEvent(event) {
      if (!this.disabled && gridBodyElementEventHandlers[event.type] && event.pointerType !== "focus") {
        this[gridBodyElementEventHandlers[event.type]](event);
      }
    }
    //endregion
    //region Touch events
    /**
     * Touch start, chain this function in features to handle the event.
     * @param event
     * @category Touch events
     * @internal
     */
    onElementTouchStart(event) {
      const me = this;
      DomHelper.isTouchEvent = true;
      if (event.touches.length === 1) {
        me.longPressTimeout = me.setTimeout(() => {
          me.onElementLongPress(event);
          event.preventDefault();
          me.longPressPerformed = true;
        }, me.longPressTime);
      }
    }
    /**
     * Touch move, chain this function in features to handle the event.
     * @param event
     * @category Touch events
     * @internal
     */
    onElementTouchMove(event) {
      const me = this, {
        lastTouchTarget
      } = me, touch = event.changedTouches[0], {
        pageX,
        pageY
      } = touch, touchTarget = event.target.getRootNode().elementFromPoint(pageX, pageY);
      if (me.longPressTimeout) {
        me.clearTimeout(me.longPressTimeout);
        me.longPressTimeout = null;
      }
      if (touchTarget !== lastTouchTarget && lastTouchTarget) {
        const mouseoutEvent = new MouseEvent("mouseout", ObjectHelper.copyProperties({
          relatedTarget: touchTarget,
          pointerType: "touch",
          bubbles: true
        }, touch, eventProps2));
        mouseoutEvent.preventDefault = () => event.preventDefault();
        touchTarget == null ? void 0 : touchTarget.dispatchEvent(mouseoutEvent);
        const mouseoverEvent = new MouseEvent("mouseover", ObjectHelper.copyProperties({
          relatedTarget: lastTouchTarget,
          pointerType: "touch",
          bubbles: true
        }, touch, eventProps2));
        mouseoverEvent.preventDefault = () => event.preventDefault();
        lastTouchTarget == null ? void 0 : lastTouchTarget.dispatchEvent(mouseoverEvent);
      }
      me.lastTouchTarget = touchTarget;
    }
    /**
     * Touch end, chain this function in features to handle the event.
     * @param event
     * @category Touch events
     * @internal
     */
    onElementTouchEnd(event) {
      const me = this;
      if (me.longPressPerformed) {
        if (event.cancelable) {
          event.preventDefault();
        }
        me.longPressPerformed = false;
      }
      if (me.longPressTimeout) {
        me.clearTimeout(me.longPressTimeout);
        me.longPressTimeout = null;
      }
    }
    onElementLongPress(event) {
    }
    //endregion
    //region Mouse events
    // Trigger events in same style when clicking, dblclicking and for contextmenu
    triggerCellMouseEvent(name, event, cellData = this.getCellDataFromEvent(event), target = "cell") {
      var _a6;
      const me = this;
      if (cellData) {
        const column = me.columns.getById(cellData.columnId), eventData = {
          grid: me,
          record: cellData.record,
          column,
          cellSelector: cellData.cellSelector,
          cellElement: cellData.cellElement,
          target: event.target,
          event
        };
        me.trigger(target + StringHelper.capitalize(name), eventData);
        if (target === "cell" && name === "click") {
          (_a6 = column.onCellClick) == null ? void 0 : _a6.call(column, eventData);
        }
      }
    }
    /**
     * Mouse down, chain this function in features to handle the event.
     * @param event
     * @category Mouse events
     * @internal
     */
    onElementMouseDown(event) {
      const me = this, cellData = me.getCellDataFromEvent(event);
      me.skipFocusSelection = true;
      if (me.isScrollbarOrRowBorderOrSplitterClick(event)) {
        event.preventDefault();
      } else {
        me.triggerCellMouseEvent("mousedown", event, cellData);
        if (cellData && !event.defaultPrevented) {
          me.onFocusGesture(event);
        }
      }
    }
    isScrollbarOrRowBorderOrSplitterClick({ target, x, y }) {
      if (target.closest(".b-grid-splitter") || target.matches(".b-grid-row, .b-virtual-width")) {
        return true;
      }
      if (target.matches(".b-vertical-overflow")) {
        const rect = target.getBoundingClientRect();
        return x > rect.right - DomHelper.scrollBarWidth;
      } else if (target.matches(".b-horizontal-overflow")) {
        const rect = target.getBoundingClientRect();
        return y > rect.bottom - DomHelper.scrollBarWidth - 1;
      }
    }
    /**
     * Mouse move, chain this function in features to handle the event.
     * @param event
     * @category Mouse events
     * @internal
     */
    onElementMouseMove(event) {
      this.mouseMoveEvent = event;
    }
    /**
     * Mouse up, chain this function in features to handle the event.
     * @param event
     * @category Mouse events
     * @internal
     */
    onElementMouseUp(event) {
    }
    onElementPointerDown(event) {
    }
    /**
     * Pointer up, chain this function in features to handle the event.
     * @param event
     * @category Mouse events
     * @internal
     */
    onElementPointerUp(event) {
    }
    /**
     * Called before {@link #function-onElementClick}.
     * Fires 'beforeElementClick' event which can return false to cancel further onElementClick actions.
     * @param event
     * @fires beforeElementClick
     * @category Mouse events
     * @internal
     */
    onHandleElementClick(event) {
      if (this.trigger("beforeElementClick", { event }) !== false) {
        this.onElementClick(event);
      }
    }
    /**
     * Click, select cell on click and also fire 'cellClick' event.
     * Chain this function in features to handle the dom event.
     * @param event
     * @fires cellClick
     * @category Mouse events
     * @internal
     */
    onElementClick(event) {
      var _a6;
      const me = this, cellData = me.getCellDataFromEvent(event);
      if (cellData) {
        return me.triggerCellMouseEvent("click", event, cellData);
      } else {
        const header = (_a6 = event.target) == null ? void 0 : _a6.closest(".b-grid-header"), column = header && me.columns.getById(header.dataset.columnId);
        if (column) {
          return me.trigger("headerClick", {
            domEvent: event,
            column
          });
        }
      }
    }
    onFocusGesture(event) {
      var _a6, _b2, _c2, _d;
      const me = this, { navigationEvent } = me, { target } = event, isContextMenu = event.button === 2, isTreeExpander = !isContextMenu && ((_c2 = (_b2 = (_a6 = me.features.tree) == null ? void 0 : _a6.treeColumn) == null ? void 0 : _b2.isTreeExpanderIcon) == null ? void 0 : _c2.call(_b2, target)), isUnfocusedRightClick = !document.hasFocus() && BrowserHelper.isMac && isContextMenu;
      if (isTreeExpander || isUnfocusedRightClick) {
        event.preventDefault();
      } else {
        me.navigationEvent = event;
        const location2 = new GridLocation(target);
        if (isContextMenu || ((_d = me.focusedCell) == null ? void 0 : _d.equals(location2))) {
          const focusOptions2 = { scroll: false };
          if (globalThis.TouchEvent && event instanceof MouseEvent && navigationEvent instanceof TouchEvent && target === navigationEvent.target) {
            focusOptions2.doSelect = false;
          }
          me.focusCell(location2, focusOptions2);
        }
      }
    }
    /**
     * Double click, fires 'cellDblClick' event.
     * Chain this function in features to handle the dom event.
     * @param {Event} event
     * @fires cellDblClick
     * @fires columnResize
     * @category Mouse events
     * @internal
     */
    onElementDblClick(event) {
      const { target } = event;
      this.triggerCellMouseEvent("dblClick", event);
      if (target.classList.contains("b-grid-header-resize-handle")) {
        const header = target.closest(".b-grid-header"), column = this.columns.getById(header.dataset.columnId);
        if (column.resizeToFitContent()) {
          this.trigger("columnResize", { column, domEvent: event });
        }
      }
    }
    shouldHover(event) {
      return (DragHelper.dragging || (typeof event.buttons !== "number" || event.buttons === 0)) && event.pointerType !== "touch";
    }
    /**
     * Mouse over, adds 'hover' class to elements.
     * @param event
     * @fires mouseOver
     * @category Mouse events
     * @internal
     */
    onElementMouseOver(event) {
      var _a6;
      if (!this.scrolling) {
        const me = this, { hoveredCell } = me, shouldHover = this.shouldHover(event);
        let cellElement = event.target.closest(".b-grid-cell");
        me._lastMouseOverOutEvent = event;
        if (!cellElement && event.target.classList.contains("b-grid-row")) {
          cellElement = (_a6 = DomHelper.childFromPoint(event.target, event.offsetX, event.offsetY - 2)) == null ? void 0 : _a6.closest(".b-grid-cell");
        }
        if (cellElement) {
          if (shouldHover) {
            me.hoveredCell = cellElement;
          }
          if (hoveredCell !== me.hoveredCell) {
            me.triggerCellMouseEvent("mouseOver", event);
          }
        }
        me.trigger("mouseOver", { event });
      }
    }
    /**
     * Mouse out, removes 'hover' class from elements.
     * @param event
     * @fires mouseOut
     * @category Mouse events
     * @internal
     */
    onElementMouseOut(event) {
      var _a6;
      const me = this, {
        target,
        relatedTarget
      } = event, shouldHover = (typeof event.buttons !== "number" || event.buttons === 0) && event.pointerType !== "touch";
      me._lastMouseOverOutEvent = event;
      if (shouldHover && relatedTarget && target.matches(".b-grid-cell") && !target.contains(relatedTarget)) {
        if (!relatedTarget.matches(".b-grid-row")) {
          me.hoveredCell = relatedTarget.closest(".b-grid-cell");
        }
      } else if (!(relatedTarget == null ? void 0 : relatedTarget.matches(".b-grid-row,.b-grid-cell")) && !((_a6 = me.hoveredCell) == null ? void 0 : _a6.contains(relatedTarget))) {
        me.hoveredCell = null;
      }
      if (!me.scrolling) {
        if ((relatedTarget == null ? void 0 : relatedTarget.closest(".b-grid-cell")) !== (target == null ? void 0 : target.closest(".b-grid-cell"))) {
          if (target == null ? void 0 : target.closest(".b-grid-cell")) {
            me.triggerCellMouseEvent("mouseOut", event);
          }
        }
        me.trigger("mouseOut", { event });
      }
    }
    // The may be chained in features
    updateHoveredCell(cellElement, was) {
      var _a6, _b2, _c2;
      const me = this, { selectionMode } = me, rowNumberColumnId = selectionMode.rowNumber && ((_a6 = me.columns.find((c) => c.type == "rownumber")) == null ? void 0 : _a6.id), checkboxSelectionColumnId = selectionMode.checkbox && ((_b2 = me.checkboxSelectionColumn) == null ? void 0 : _b2.id);
      let prevRow, row;
      if (was) {
        toggleHover(was, false);
        const prevSelector = DomDataStore.get(was);
        prevRow = prevSelector.row;
        if (prevRow && !prevRow.isDestroyed) {
          setCellHover(rowNumberColumnId, prevRow, false);
          setCellHover(checkboxSelectionColumnId, prevRow, false);
        }
        if (prevSelector == null ? void 0 : prevSelector.columnId) {
          toggleHover((_c2 = me.columns.getById(prevSelector.columnId)) == null ? void 0 : _c2.element, false);
        }
      }
      if (me._hoveredRow && !me._hoveredRow.isDestroyed) {
        me.toggleRowHover(me._hoveredRow, false);
      }
      me._hoveredRow = null;
      if (cellElement && !me.scrolling) {
        const selector = DomDataStore.get(cellElement);
        row = selector.row;
        if (row) {
          if (selectionMode.cell && selector.columnId !== rowNumberColumnId && selector.columnId !== checkboxSelectionColumnId) {
            const column = me.columns.getById(selector.columnId);
            toggleHover(cellElement);
            setCellHover(checkboxSelectionColumnId, row);
            setCellHover(rowNumberColumnId, row);
            if ((column == null ? void 0 : column.element) && column.headerHoverable !== false) {
              toggleHover(column.element);
            }
          } else {
            me._hoveredRow = row;
            me.toggleRowHover(row, true);
          }
        } else {
          me.hoveredCell = null;
        }
      }
      if (row !== prevRow) {
        const { _lastMouseOverOutEvent } = me;
        if (prevRow && !prevRow.isDestroyed) {
          me.trigger("rowMouseLeave", { event: _lastMouseOverOutEvent, record: prevRow.cellContext.record, cellElement: was });
        }
        if (row) {
          const cellData = me.getCellDataFromEvent(_lastMouseOverOutEvent);
          me.triggerCellMouseEvent("mouseEnter", _lastMouseOverOutEvent, cellData, "row");
        }
      }
    }
    toggleRowHover(row, hover) {
      if (this.hoverCls) {
        row.toggleCls(this.hoverCls, hover);
      }
    }
    //endregion
    //region Keyboard events
    // Hooks on to keyMaps keydown-listener to be able to run before
    keyMapOnKeyDown(event) {
      var _a6;
      const me = this, { target } = event.browserEvent;
      if (me.element.contains(target)) {
        const isArrowKey2 = event.key.startsWith("Arrow"), isActionKey = event.key === "Escape" || event.key === "F2" || event.key === "Tab" || event.key === "Enter";
        me.onElementKeyDown(event);
        if ((textKeys[event.key] && DomHelper.isEditable(target, true) || ((_a6 = me.focusedCell) == null ? void 0 : _a6.containsFocus) && !isActionKey && !isArrowKey2 && !me.matchKeyMapEntry(event)) && !target.getAttribute("readOnly") && me.matchKeyMapEntry(event)) {
          if (!isArrowKey2 && !event.key === "Backspace") {
            event.preventDefault();
          }
          event.handled = true;
          event.stopPropagation();
          return false;
        } else {
          super.keyMapOnKeyDown(event);
        }
      }
    }
    /**
     * To catch all keydowns. For more specific keydown actions, use keyMap.
     * @param event
     * @category Keyboard events
     * @internal
     */
    onElementKeyDown(event) {
      var _a6, _b2;
      if (event.handled || !this.element.contains(event.target)) {
        return;
      }
      const me = this, focusedCell = me.focusedCell;
      if ((focusedCell == null ? void 0 : focusedCell.isCell) && !focusedCell.isActionable) {
        const data = event.target.closest(".b-grid-cell") ? me.getCellDataFromEvent(event) : me.getCellDataFromElement(focusedCell.cell);
        (_b2 = data == null ? void 0 : (_a6 = data.column).onCellKeyDown) == null ? void 0 : _b2.call(_a6, { ...data, event });
      }
    }
    undoRedoKeyPress(event) {
      var _a6;
      const { stm } = this.store;
      if (stm && this.enableUndoRedoKeys && !((_a6 = this.features.cellEdit) == null ? void 0 : _a6.isEditing)) {
        stm.onUndoKeyPress(event);
        return true;
      }
      return false;
    }
    // Trigger column.onCellClick when space bar is pressed
    clickCellByKey(event) {
      const me = this, focusedCell = me.focusedCell, cellElement = focusedCell == null ? void 0 : focusedCell.cell, column = me.columns.getById(cellElement.dataset.columnId);
      if ((focusedCell == null ? void 0 : focusedCell.isCell) && !focusedCell.isActionable) {
        if (column.onCellClick) {
          column.onCellClick({
            grid: me,
            column,
            record: me.store.getById(focusedCell.id),
            cellElement,
            target: event.target,
            event
          });
          return true;
        }
      }
      return false;
    }
    /**
     * Key press, chain this function in features to handle the dom event.
     * @param event
     * @category Keyboard events
     * @internal
     */
    onElementKeyPress(event) {
    }
    /**
     * Key up, chain this function in features to handle the dom event.
     * @param event
     * @category Keyboard events
     * @internal
     */
    onElementKeyUp(event) {
    }
    //endregion
    //region Other events
    /**
     * Context menu, chain this function in features to handle the dom event.
     * In most cases, include ContextMenu feature instead.
     * @param event
     * @category Other events
     * @internal
     */
    onElementContextMenu(event) {
      const me = this, cellData = me.getCellDataFromEvent(event);
      if (cellData) {
        me.triggerCellMouseEvent("contextMenu", event, cellData);
        if (DomHelper.isTouchEvent) {
          me.onFocusGesture(event);
        }
      }
    }
    onGridBodyMouseEnterOrLeave(event) {
      const cellData = event.target.classList.contains("b-grid-cell") && this.getCellDataFromEvent(event);
      if (cellData) {
        this.triggerCellMouseEvent(event.type, event, cellData);
      }
    }
    /**
     * Overrides empty base function in View, called when view is resized.
     * @fires resize
     * @param element
     * @param width
     * @param height
     * @param oldWidth
     * @param oldHeight
     * @category Other events
     * @internal
     */
    onInternalResize(element, width, height, oldWidth, oldHeight) {
      const me = this;
      if (me._devicePixelRatio && me._devicePixelRatio !== globalThis.devicePixelRatio) {
        DomHelper.resetScrollBarWidth();
      }
      me._devicePixelRatio = globalThis.devicePixelRatio;
      me._bodyRectangle = Rectangle.client(me.bodyContainer);
      super.onInternalResize(...arguments);
      if (height !== oldHeight) {
        me._bodyHeight = me.bodyContainer.offsetHeight;
        if (me.isPainted) {
          me.rowManager.initWithHeight(me._bodyHeight);
        }
      }
      me.refreshVirtualScrollbars();
      if (width !== oldWidth) {
        me.setTimeout(() => {
          if (!me.isDestroyed) {
            me.updateResponsive(width, oldWidth);
          }
        }, 0);
      }
    }
    //endregion
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "GridElementEvents"), _a5;
};

// ../Grid/lib/Grid/view/mixin/GridFeatures.js
var validConfigTypes = {
  string: 1,
  object: 1,
  function: 1
  // used by CellTooltip
};
var GridFeatures_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    //region Init
    /**
     * Specify which features to use on the grid. Most features accepts a boolean, some also accepts a config object.
     * Please note that if you are not using the bundles you might need to import the features you want to use.
     *
     * ```javascript
     * const grid = new Grid({
     *     features : {
     *         stripe : true,   // Enable stripe feature
     *         sort   : 'name', // Configure sort feature
     *         group  : false   // Disable group feature
     *     }
     * }
     * ```
     *
     * @config {Object} features
     * @category Common
     */
    /**
     * Map of the features available on the grid. Use it to access them on your grid object
     *
     * ```javascript
     * grid.features.group.expandAll();
     * ```
     *
     * @readonly
     * @member {Object} features
     * @category Common
     */
    set features(features) {
      const me = this, defaultFeatures = GridFeatureManager.getInstanceDefaultFeatures(this);
      features = me._features = ObjectHelper.merge({}, this.constructor.configurable.features, ObjectHelper.isObject(features) ? features : {});
      if (defaultFeatures) {
        Object.keys(defaultFeatures).forEach((feature) => {
          if (!(feature in features)) {
            features[feature] = true;
          }
        });
      }
      const registeredInstanceFeatures = GridFeatureManager.getInstanceFeatures(this);
      for (const featureName of Object.keys(features)) {
        const config = features[featureName];
        if (config) {
          const throwIfError = !globalThis.__bryntum_code_editor_changed;
          if (StringHelper.uncapitalize(featureName) !== featureName) {
            const errorMessage = `Invalid feature name '${featureName}', must start with a lowercase letter`;
            if (throwIfError) {
              throw new Error(errorMessage);
            }
            console.error(errorMessage);
            me._errorDuringConfiguration = errorMessage;
          }
          const featureClass = registeredInstanceFeatures[featureName];
          if (!featureClass) {
            const errorMessage = `Feature '${featureName}' not available, make sure you have imported it`;
            if (throwIfError) {
              throw new Error(errorMessage);
            }
            console.error(errorMessage);
            me._errorDuringConfiguration = errorMessage;
            return;
          }
          Reflect.defineProperty(features, featureName, me.createFeatureInitializer(
            features,
            featureName,
            featureClass,
            config
          ));
        }
      }
    }
    get features() {
      return this._features;
    }
    createFeatureInitializer(features, featureName, featureClass, config) {
      const constructorArgs = [this], construct = featureClass.prototype.construct;
      if (config === true) {
        config = {};
      }
      if (validConfigTypes[typeof config]) {
        constructorArgs[1] = config;
      }
      return {
        configurable: true,
        get() {
          delete features[featureName];
          featureClass.prototype.construct = function(...args) {
            features[featureName] = this;
            construct.apply(this, args);
            featureClass.prototype.construct = construct;
          };
          return new featureClass(...constructorArgs);
        }
      };
    }
    //endregion
    //region Other stuff
    /**
     * Check if a feature is included
     * @param {String} name Feature name, as registered with `GridFeatureManager.registerFeature()`
     * @returns {Boolean}
     * @category Misc
     */
    hasFeature(name) {
      const { features } = this;
      if (features) {
        const featureProp = Object.getOwnPropertyDescriptor(this.features, name);
        if (featureProp) {
          return Boolean(featureProp.value || featureProp.get);
        }
      }
      return false;
    }
    hasActiveFeature(name) {
      var _a6, _b2;
      return Boolean((_b2 = (_a6 = this.features) == null ? void 0 : _a6[name]) == null ? void 0 : _b2.enabled);
    }
    //endregion
    //region Extract config
    // This function is not meant to be called by any code other than Base#getCurrentConfig().
    // It extracts the current configs for the features
    getConfigValue(name, options) {
      var _a6, _b2;
      if (name === "features") {
        const result = {};
        for (const feature in this.features) {
          const featureConfig = (_b2 = (_a6 = this.features[feature]) == null ? void 0 : _a6.getCurrentConfig) == null ? void 0 : _b2.call(_a6, options);
          if (featureConfig) {
            if (ObjectHelper.isEmpty(featureConfig)) {
              if (!GridFeatureManager.isDefaultFeatureForInstance(this.features[feature].constructor, this)) {
                result[feature] = true;
              }
            } else {
              result[feature] = featureConfig;
            }
          } else {
            result[feature] = false;
          }
        }
        return result;
      }
      return super.getConfigValue(name, options);
    }
    //endregion
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "GridFeatures"), _a5;
};

// ../Grid/lib/Grid/view/mixin/GridNavigation.js
var defaultFocusOptions = Object.freeze({});
var interactiveSelector = 'button, input, textarea, a, [contenteditable="true"], select, option';
var containedFocusable = function(e) {
  if (!this.focusableFinderCell.contains(e)) {
    return DomHelper.NodeFilter.FILTER_REJECT;
  }
  if (DomHelper.isFocusable(e) && !e.disabled) {
    return DomHelper.NodeFilter.FILTER_ACCEPT;
  }
  return DomHelper.NodeFilter.FILTER_SKIP;
};
var GridNavigation_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    onStoreRecordIdChange(event) {
      var _a6;
      (_a6 = super.onStoreRecordIdChange) == null ? void 0 : _a6.call(this, event);
      const { focusedCell } = this, { oldValue, value: value2 } = event;
      if (focusedCell && focusedCell.id === oldValue) {
        focusedCell._id = value2;
      }
    }
    /**
     * Called by the RowManager when the row which contains the focus location is derendered.
     *
     * This keeps focus in a consistent place.
     * @protected
     */
    onFocusedRowDerender() {
      const me = this, { focusedCell } = me;
      if ((focusedCell == null ? void 0 : focusedCell.id) != null && focusedCell.cell) {
        const isActive2 = DomHelper.isDescendant(focusedCell.cell, DomHelper.getActiveElement(me));
        if (me.hideHeaders) {
          if (isActive2) {
            me.revertFocus();
          }
        } else {
          const headerContext = me.normalizeCellContext({
            rowIndex: -1,
            columnIndex: isActive2 ? focusedCell.columnIndex : 0
          });
          if (isActive2) {
            me.focusCell(headerContext);
          } else {
            headerContext.cell.tabIndex = 0;
          }
        }
        focusedCell.cell.tabIndex = -1;
      }
    }
    navigateFirstCell() {
      this.focusCell(GridLocation.FIRST_CELL);
    }
    navigateFirstColumn() {
      this.focusCell(GridLocation.FIRST_COLUMN);
    }
    navigateLastCell() {
      this.focusCell(GridLocation.LAST_CELL);
    }
    navigateLastColumn() {
      this.focusCell(GridLocation.LAST_COLUMN);
    }
    navigatePrevPage() {
      this.focusCell(GridLocation.PREV_PAGE);
    }
    navigateNextPage() {
      this.focusCell(GridLocation.NEXT_PAGE);
    }
    activateCell(keyEvent) {
      var _a6, _b2;
      const { focusedCell } = this;
      if (focusedCell.isColumnHeader) {
        if (keyEvent.target.classList.contains("b-grid-header")) {
          const { column } = this.focusedCell;
          (_a6 = column.onKeyDown) == null ? void 0 : _a6.call(column, keyEvent);
          this.getHeaderElement(column.id).click();
        }
      } else if (!focusedCell.isActionable && ((_b2 = focusedCell.actionTargets) == null ? void 0 : _b2.length)) {
        keyEvent.preventDefault();
        focusedCell.actionTargets[0].focus();
      }
      return false;
    }
    toggleCellActivate(keyEvent) {
      var _a6;
      const { focusedCell } = this;
      if ((_a6 = focusedCell == null ? void 0 : focusedCell.actionTargets) == null ? void 0 : _a6.length) {
        if (focusedCell.isActionable) {
          this.deactivateCell(keyEvent);
        } else {
          focusedCell.actionTargets[0].focus();
        }
      }
    }
    deactivateCell(keyEvent) {
      var _a6, _b2;
      const me = this, { focusedCell } = me;
      if (!keyEvent.target.closest(".b-dragging") && (focusedCell == null ? void 0 : focusedCell.isActionable)) {
        keyEvent.stopImmediatePropagation();
        me._focusedCell = null;
        me.focusCell({
          rowIndex: focusedCell.rowIndex,
          column: focusedCell.column
        }, {
          disableActionable: true
        });
      } else if (me.isNested && me.owner && !((_b2 = (_a6 = me.owner).catchFocus) == null ? void 0 : _b2.call(_a6, { source: me }))) {
        me.revertFocus(true);
      }
    }
    onTab(keyEvent) {
      const { target } = keyEvent, {
        focusedCell,
        bodyElement
      } = this, {
        isActionable,
        actionTargets = []
      } = focusedCell, isEditable = isActionable && DomHelper.isEditable(target) && !target.readOnly;
      if (isEditable && target === actionTargets[actionTargets.length - 1]) {
        keyEvent.preventDefault();
        this.navigateRight(keyEvent);
      } else if (!isActionable || target === actionTargets[actionTargets.length - 1]) {
        bodyElement.setAttribute("inert", true);
        this.requestAnimationFrame(() => bodyElement.removeAttribute("inert"));
        return false;
      }
    }
    onShiftTab(keyEvent) {
      const me = this, { target } = keyEvent, {
        focusedCell,
        bodyElement
      } = me, {
        isActionable,
        actionTargets
      } = focusedCell, isEditable = isActionable && DomHelper.isEditable(target) && !target.readOnly, onFirstCell = focusedCell.columnIndex === 0 && focusedCell.rowIndex === (me.hideHeaders ? 0 : -1);
      if (!onFirstCell && isEditable && target === actionTargets[0]) {
        keyEvent.preventDefault();
        me.navigateLeft(keyEvent);
      } else if (!isActionable || target === actionTargets[0]) {
        bodyElement.setAttribute("inert", true);
        this.requestAnimationFrame(() => bodyElement.removeAttribute("inert"));
        return false;
      }
    }
    onSpace(keyEvent) {
      if (!this.focusedCell.isActionable) {
        keyEvent.preventDefault();
      }
      return false;
    }
    //region Cell
    /**
     * Triggered when a user navigates to a grid cell
     * @event navigate
     * @param {Event} [event] The UI event which caused navigation.
     * @param {String} eventName The name of the event
     * @param {Grid.util.GridLocation} focusedCell The currently focused cell
     * @param {Grid.util.GridLocation} lastFocusedCell The previously focused cell
     * @param {Grid.view.Grid} source The grid instance
     * @param {String} type The type of the event
     */
    /**
     * Grid GridLocation which encapsulates the currently focused cell.
     * Set to focus a cell or use {@link #function-focusCell}.
     * @property {Grid.util.GridLocation}
     */
    get focusedCell() {
      return this._focusedCell;
    }
    /**
     * This property is `true` if an element _within_ a cell is focused.
     * @property {Boolean}
     * @readonly
     */
    get isActionableLocation() {
      var _a6;
      return (_a6 = this._focusedCell) == null ? void 0 : _a6.isActionable;
    }
    set focusedCell(cellSelector) {
      this.focusCell(cellSelector);
    }
    get focusedRecord() {
      var _a6;
      return (_a6 = this._focusedCell) == null ? void 0 : _a6.record;
    }
    /**
     * CSS selector for currently focused cell. Format is "[data-index=index] [data-column-id=columnId]".
     * @property {String}
     * @readonly
     */
    get cellCSSSelector() {
      const cell = this._focusedCell;
      return cell ? `[data-index=${cell.rowIndex}] [data-column-id=${cell.columnId}]` : "";
    }
    afterHide() {
      super.afterHide(...arguments);
      this.lastFocusedCell = null;
    }
    /**
     * Checks whether a cell is focused.
     * @param {GridLocationConfig|Grid.util.GridLocation|String|Number} cellSelector Cell selector { id: x, columnId: xx } or row id
     * @returns {Boolean} true if cell or row is focused, otherwise false
     */
    isFocused(cellSelector) {
      var _a6;
      return Boolean((_a6 = this._focusedCell) == null ? void 0 : _a6.equals(this.normalizeCellContext(cellSelector)));
    }
    get focusElement() {
      var _a6;
      if (!this.isDestroying) {
        let focusCell;
        if (this.store.count && this._focusedCell) {
          focusCell = this._focusedCell.target;
        } else if (!this.hideHeaders) {
          focusCell = this.normalizeCellContext({
            rowIndex: -1,
            columnIndex: ((_a6 = this._focusedCell) == null ? void 0 : _a6.columnIndex) || 0
          }).target;
        }
        const superFocusEl = super.focusElement;
        if (superFocusEl && (!focusCell || focusCell.compareDocumentPosition(superFocusEl) === Node.DOCUMENT_POSITION_PRECEDING)) {
          return superFocusEl;
        }
        return focusCell;
      }
    }
    afterInternalPaint({ firstPaint }) {
      var _a6, _b2;
      const me = this;
      (_a6 = super.afterInternalPaint) == null ? void 0 : _a6.call(this, ...arguments);
      const defaultFocus = this.normalizeCellContext({
        rowIndex: me.hideHeaders ? 0 : -1,
        column: me.hideHeaders ? 0 : me.columns.find((col) => !col.hidden && col.isFocusable)
      });
      if ((_b2 = defaultFocus.cell) == null ? void 0 : _b2.getAttribute("tabIndex")) {
        defaultFocus._isDefaultFocus = true;
        me._focusedCell = defaultFocus;
        const { target } = defaultFocus;
        if (target === defaultFocus.cell) {
          defaultFocus.cell.tabIndex = 0;
        }
      }
    }
    /**
     * This function handles focus moving into, or within the grid.
     * @param {Event} focusEvent
     * @private
     */
    onGridBodyFocusIn(focusEvent) {
      var _a6, _b2, _c2, _d;
      const me = this, { bodyElement } = me, lastFocusedCell = me.focusedCell, lastTarget = (lastFocusedCell == null ? void 0 : lastFocusedCell.initialTarget) || (lastFocusedCell == null ? void 0 : lastFocusedCell.target), {
        relatedTarget
      } = focusEvent, target = focusEvent.browserEvent.target, targetCell = target.closest(me.focusableSelector), targetRow = target.closest(".b-grid-row");
      if (targetCell && (!GlobalEvents_default.currentMouseDown || GlobalEvents_default.isMouseDown(0) || GlobalEvents_default.isMouseDown(2))) {
        const cellSelector = new GridLocation(target), { cell } = cellSelector, lastCell = lastFocusedCell == null ? void 0 : lastFocusedCell.cell, actionTargets = cellSelector.actionTargets = me.findFocusables(targetCell), doSelect = (!me._fromFocusCell || me.selectOnFocus) && (target === cell || !targetCell.contains(target.closest(interactiveSelector)) || me._selectActionCell) && !(target == null ? void 0 : target._isRevertingFocus);
        if (targetRow && cellSelector.rowIndex === -1) {
          cell.focus({ preventScroll: true });
          return;
        }
        if (target.matches(me.focusableSelector)) {
          if (me.disableActionable) {
            cellSelector._target = cell;
          } else if (actionTargets.length) {
            const currentMouseDownTarget = (_a6 = GlobalEvents_default.currentMouseDown) == null ? void 0 : _a6.target;
            if (!actionTargets.some((el) => el.contains(currentMouseDownTarget))) {
              (_b2 = me.onCellNavigate) == null ? void 0 : _b2.call(me, me, lastFocusedCell, cellSelector, doSelect);
              me.trigger("navigate", { lastFocusedCell, focusedCell: cellSelector, event: focusEvent });
            }
            me._selectActionCell = currentMouseDownTarget === target;
            actionTargets[0].focus();
            delete me._selectActionCell;
            return;
          }
        } else {
          if ((lastFocusedCell == null ? void 0 : lastFocusedCell.target) && relatedTarget && (!GlobalEvents_default.isMouseDown() || !bodyElement.contains((_c2 = GlobalEvents_default.currentMouseDown) == null ? void 0 : _c2.target)) && !bodyElement.contains(relatedTarget) && !cellSelector.equals(lastFocusedCell)) {
            lastTarget.focus();
            return;
          }
          cellSelector._target = target;
        }
        if (lastCell) {
          lastCell.classList.remove("b-focused");
          lastCell.tabIndex = -1;
        }
        if (cell) {
          cell.classList.add("b-focused");
          cellSelector.column.onCellFocus(cellSelector);
          if (cell === target) {
            cell.tabIndex = 0;
          }
          if (cell.contains(focusEvent.relatedTarget)) {
            if (lastTarget === target) {
              return;
            }
          }
        }
        me._focusedCell = cellSelector;
        (_d = me.onCellNavigate) == null ? void 0 : _d.call(me, me, lastFocusedCell, cellSelector, doSelect);
        me.trigger("navigate", { lastFocusedCell, focusedCell: cellSelector, event: focusEvent });
      } else if (!target.closest(".b-rowexpander-body")) {
        lastTarget == null ? void 0 : lastTarget.focus({ preventScroll: true });
      }
    }
    findFocusables(cell) {
      const { focusableFinder } = this, result = [];
      focusableFinder.currentNode = this.focusableFinderCell = cell;
      for (let focusable = focusableFinder.nextNode(); focusable; focusable = focusableFinder.nextNode()) {
        result.push(focusable);
      }
      return result;
    }
    get focusableFinder() {
      const me = this;
      if (!me._focusableFinder) {
        me._focusableFinder = me.setupTreeWalker(me.bodyElement, DomHelper.NodeFilter.SHOW_ELEMENT, {
          acceptNode: containedFocusable.bind(me)
        });
      }
      return me._focusableFinder;
    }
    /**
     * Sets the passed record as the current focused record for keyboard navigation and selection purposes.
     * This API is used by Combo to activate items in its picker.
     * @param {Core.data.Model|Number|String} activeItem The record, or record index, or record id to highlight as the active ("focused") item.
     * @internal
     */
    restoreActiveItem(item = this._focusedCell) {
      if (this.rowManager.count) {
        if (!isNaN(item)) {
          item = this.store.getAt(item);
        } else if (!item.isModel) {
          item = this.store.getById(item);
        }
        return this.focusCell(item);
      }
    }
    /**
     * Navigates to a cell and/or its row (depending on selectionMode)
     * @param {GridLocationConfig|Grid.util.GridLocation} cellSelector Cell location descriptor
     * @param {Object} options Modifier options for how to deal with focusing the cell. These
     * are used as the {@link Core.helper.util.Scroller#function-scrollTo} options.
     * @param {BryntumScrollOptions|Boolean} [options.scroll=true] Pass `false` to not scroll the cell into view, or a
     * scroll options object to affect the scroll.
     * @returns {Grid.util.GridLocation} A `GridLocation` object representing the focused location.
     * @fires navigate
     */
    focusCell(cellSelector, options = defaultFocusOptions) {
      var _a6, _b2, _c2, _d;
      const me = this, { _focusedCell } = me, {
        scroll,
        disableActionable
      } = options, isDown = cellSelector === GridLocation.DOWN, isUp = cellSelector === GridLocation.UP, isLeftRightKey = cellSelector === GridLocation.PREV_CELL || cellSelector === GridLocation.NEXT_CELL, { cellEdit } = me.features;
      if ((cellSelector == null ? void 0 : cellSelector.rowIndex) === -1 && me.hideHeaders) {
        me.revertFocus();
        return;
      }
      if (typeof cellSelector === "number" && !(_focusedCell == null ? void 0 : _focusedCell.isLocation)) {
        return;
      }
      cellSelector = typeof cellSelector === "number" && (_focusedCell == null ? void 0 : _focusedCell.isLocation) ? _focusedCell.move(cellSelector) : me.normalizeCellContext(cellSelector);
      const doSelect = "doSelect" in options ? options.doSelect : !cellSelector.isActionable || cellSelector.initialTarget === cellSelector.cell;
      if (cellSelector.equals(_focusedCell)) {
        if (me.isNested && (isDown || isUp)) {
          if (!((_b2 = (_a6 = me.owner) == null ? void 0 : _a6.catchFocus) == null ? void 0 : _b2.call(_a6, { source: me, navigationDirection: isDown ? "down" : "up" }))) {
            me.revertFocus(true);
          }
        } else {
          (_c2 = me.onCellNavigate) == null ? void 0 : _c2.call(me, me, _focusedCell, cellSelector, doSelect);
        }
        return _focusedCell;
      }
      if ((cellEdit == null ? void 0 : cellEdit.isEditing) && !cellSelector.equals(cellEdit.editorContext) && cellEdit.getEditingContext(cellSelector)) {
        cellEdit.startEditing(cellSelector);
        return cellSelector;
      }
      const subGrid = me.getSubGridFromColumn(cellSelector.columnId), { cell } = cellSelector, testCell = cell || me.getCell({
        rowIndex: me.rowManager.topIndex,
        columnId: cellSelector.columnId
      }), subGridRect = Rectangle.from(subGrid.element), bodyRect = Rectangle.from(me.bodyElement), cellRect = Rectangle.from(testCell).moveTo(null, subGridRect.y);
      if (scroll !== false) {
        if (cellSelector.rowIndex === -1) {
          if (isLeftRightKey) {
            me.scrollColumnIntoView(cellSelector.column);
          }
        } else {
          options = Object.assign({}, options, scroll);
          const {
            cellIsVisibleWidthThreshold,
            cellIsVisibleHeightThreshold
          } = me, minCellWidth = cellIsVisibleWidthThreshold * subGridRect.width, minCellHeight = cellIsVisibleHeightThreshold * bodyRect.height, viewIntersection = subGridRect.intersect(cellRect), yIntersection = Math.min(bodyRect.bottom, cellRect.bottom) - Math.max(bodyRect.y, cellRect.y);
          if (cellRect.width > subGridRect.width || cellRect.height > bodyRect.height) {
            if (cellRect.width > subGridRect.width && viewIntersection.width >= minCellWidth) {
              options.x = false;
            }
            if (cellRect.height > bodyRect.height && yIntersection >= minCellHeight) {
              options.y = false;
            }
          }
          options.column = cellSelector.columnId;
          me.scrollRowIntoView(cellSelector.id, options);
        }
      }
      if (me._hoveredRow || me.hoveredCell) {
        me.hoveredCell = null;
      }
      me.disableActionable = disableActionable;
      me.selectOnFocus = doSelect;
      me._fromFocusCell = true;
      (_d = cellSelector[disableActionable ? "cell" : "target"]) == null ? void 0 : _d.focus({ preventScroll: true });
      me.disableActionable = me.selectOnFocus = false;
      delete me._fromFocusCell;
      return cellSelector;
    }
    blurCell(cellSelector) {
      const me = this, cell = me.getCell(cellSelector);
      if (cell) {
        cell.classList.remove("b-focused");
      }
    }
    clearFocus(fullClear) {
      const me = this;
      if (me._focusedCell) {
        me.lastFocusedCell = fullClear ? null : me._focusedCell;
        me.blurCell(me._focusedCell);
        me._focusedCell = null;
      }
    }
    // For override-ability
    catchFocus() {
    }
    /**
     * Selects the cell before or after currently focused cell.
     * @private
     * @param next Specify true to select the next cell, false to select the previous
     * @returns {Object} Used cell selector
     */
    internalNextPrevCell(next = true) {
      const me = this, cellSelector = me._focusedCell;
      if (cellSelector) {
        return me.focusCell({
          id: cellSelector.id,
          columnId: me.columns.getAdjacentVisibleLeafColumn(cellSelector.column, next, true).id
        });
      }
      return null;
    }
    /**
     * Select the cell after the currently focused one.
     * @returns {Grid.util.GridLocation} Cell selector
     */
    navigateRight() {
      var _a6;
      if ((_a6 = arguments[0]) == null ? void 0 : _a6.fromKeyMap) {
        return this.focusCell(this.rtl ? GridLocation.PREV_CELL : GridLocation.NEXT_CELL);
      }
      return this.internalNextPrevCell(!this.rtl);
    }
    /**
     * Select the cell before the currently focused one.
     * @returns {Grid.util.GridLocation} Cell selector
     */
    navigateLeft() {
      var _a6;
      if ((_a6 = arguments[0]) == null ? void 0 : _a6.fromKeyMap) {
        return this.focusCell(this.rtl ? GridLocation.NEXT_CELL : GridLocation.PREV_CELL);
      }
      return this.internalNextPrevCell(Boolean(this.rtl));
    }
    //endregion
    //region Row
    /**
     * Selects the next or previous record in relation to the current selection. Scrolls into view if outside.
     * @private
     * @param next Next record (true) or previous (false)
     * @param {Boolean} [skipSpecialRows=true] True to not return specialRows like headers
     * @param {Boolean} [moveToHeader=true] True to allow focus to move to a header
     * @returns {Grid.util.GridLocation|Boolean} Selection context for the focused row (& cell) or false if no selection was made
     */
    internalNextPrevRow(next, skipSpecialRows = true, moveToHeader = true) {
      const me = this, cell = me._focusedCell;
      if (!cell)
        return false;
      const record = me.store[`get${next ? "Next" : "Prev"}`](cell.id, false, skipSpecialRows);
      if (record) {
        return me.focusCell({
          id: record.id,
          columnId: cell.columnId,
          scroll: {
            x: false
          }
        });
      } else if (!next && moveToHeader && !cell.isColumnHeader) {
        this.clearFocus();
        this.getHeaderElement(cell.columnId).focus();
      }
      return false;
    }
    /**
     * Navigates to the cell below the currently focused cell
     * @returns {Grid.util.GridLocation} Selector for focused row (& cell)
     */
    navigateDown() {
      var _a6;
      if ((_a6 = arguments[0]) == null ? void 0 : _a6.fromKeyMap) {
        return this.focusCell(GridLocation.DOWN);
      }
      return this.internalNextPrevRow(true, false);
    }
    /**
     * Navigates to the cell above the currently focused cell
     * @returns {Grid.util.GridLocation} Selector for focused row (& cell)
     */
    navigateUp() {
      var _a6;
      if ((_a6 = arguments[0]) == null ? void 0 : _a6.fromKeyMap) {
        return this.focusCell(GridLocation.UP);
      }
      return this.internalNextPrevRow(false, false);
    }
    //endregion
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "GridNavigation"), __publicField(_a5, "configurable", {
    focusable: false,
    // Cells, and headers which have a tabIndex are focus targets.
    // If hideHeaders is set, the headers must not have tabIndex set
    focusableSelector: ".b-grid-cell,.b-grid-header.b-depth-0[tabIndex]",
    // Set to `true` to revert focus on Esc or on ArrowUp/ArrowDown above/below first/last row
    isNested: false,
    // Documented on Grid
    // See https://www.w3.org/WAI/ARIA/apg/patterns/grid/ for keyboard navigation standards
    keyMap: {
      ArrowUp: { handler: "navigateUp", weight: 10 },
      ArrowRight: { handler: "navigateRight", weight: 10 },
      ArrowDown: { handler: "navigateDown", weight: 10 },
      ArrowLeft: { handler: "navigateLeft", weight: 10 },
      "Ctrl+Home": "navigateFirstCell",
      Home: "navigateFirstColumn",
      "Ctrl+End": "navigateLastCell",
      End: "navigateLastColumn",
      PageUp: "navigatePrevPage",
      PageDown: "navigateNextPage",
      Enter: "activateCell",
      F2: "toggleCellActivate",
      // Private
      Escape: { handler: "deactivateCell", weight: 10 },
      "Shift+Tab": { handler: "onShiftTab", preventDefault: false, weight: 200 },
      Tab: { handler: "onTab", preventDefault: false, weight: 200 },
      " ": { handler: "onSpace", preventDefault: false }
    },
    // Thresholds used for detecting whether a cell is visible.
    // Used in "focusCell" method.
    // Specified as percent of the grid scroller width/height respectively.
    // For example xxxWidthThreshold=0.5.means if 50% of a huge cell width is visible
    // then no extra horizontal scrolling to the cell is needed
    cellIsVisibleWidthThreshold: 0.6,
    cellIsVisibleHeightThreshold: 0.6
  }), _a5;
};

// ../Grid/lib/Grid/view/mixin/GridResponsive.js
var GridResponsive_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    static get defaultConfig() {
      return {
        /**
         * "Break points" for which responsive config to use for columns and css.
         * @config {Object<String,Number|String>}
         * @category Misc
         * @default { small : 400, medium : 600, large : '*' }
         */
        responsiveLevels: Object.freeze({
          small: 400,
          medium: 600,
          large: "*"
        })
      };
    }
    /**
     * Find closes bigger level, aka level we want to use.
     * @private
     * @category Misc
     */
    getClosestBiggerLevel(width) {
      const me = this, levels = Object.keys(ObjectHelper.assign({}, me.responsiveLevels));
      let useLevel = null, minDelta = 99995, biggestLevel = null;
      levels.forEach((level) => {
        let levelSize = me.responsiveLevels[level];
        if (!["number", "string"].includes(typeof levelSize)) {
          levelSize = levelSize.levelWidth;
        }
        if (levelSize === "*") {
          biggestLevel = level;
        } else if (width < levelSize) {
          const delta = levelSize - width;
          if (delta < minDelta) {
            minDelta = delta;
            useLevel = level;
          }
        }
      });
      return useLevel || biggestLevel;
    }
    /**
     * Get currently used responsive level (as string)
     * @property {String}
     * @readonly
     * @category Misc
     */
    get responsiveLevel() {
      return this.getClosestBiggerLevel(this.width);
    }
    /**
     * Check if resize lead to a new responsive level and take appropriate actions
     * @private
     * @fires responsive
     * @param width
     * @param oldWidth
     * @category Misc
     */
    updateResponsive(width, oldWidth) {
      const me = this, oldLevel = me.getClosestBiggerLevel(oldWidth), level = me.getClosestBiggerLevel(width);
      if (oldWidth === 0 || oldLevel !== level) {
        const levelConfig = me.responsiveLevels[level];
        if (!["number", "string"].includes(typeof levelConfig)) {
          me.applyState(levelConfig);
        }
        me.columns.forEach((column) => {
          const levels = column.responsiveLevels;
          if (levels) {
            if (levels[level]) {
              column.applyState(levels[level]);
            } else if (levels["*"]) {
              column.applyState(levels["*"]);
            }
          }
        });
        me.element.classList.remove("b-responsive-" + oldLevel);
        me.element.classList.add("b-responsive-" + level);
        me.trigger("responsive", { level, width, oldLevel, oldWidth });
      }
    }
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "GridResponsive"), _a5;
};

// ../Grid/lib/Grid/view/mixin/GridSelection.js
var validIdTypes = {
  string: 1,
  number: 1
};
var isDataLoadAction = {
  dataset: 1,
  batch: 1
};
var GridSelection_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    construct(config) {
      this._selectedCells = [];
      this._selectedRows = [];
      super.construct(config);
      if (config == null ? void 0 : config.selectedRecords) {
        this.selectedRecords = config.selectedRecords;
      }
    }
    //region Init
    getDefaultGridSelection(clas) {
      if (clas.$name === "GridSelection") {
        return clas.configurable.selectionMode;
      } else if (clas.superclass) {
        return this.getDefaultGridSelection(clas.superclass);
      }
    }
    changeSelectionMode(mode) {
      const me = this;
      if (me.selectionMode) {
        ObjectHelper.assign(me.selectionMode, mode);
        return me.selectionMode;
      }
      me.$defaultGridSelection = me.getDefaultGridSelection(me.constructor);
      return new Proxy(mode, {
        set(obj, prop, value2) {
          const old = ObjectHelper.assign({}, obj);
          obj[prop] = value2;
          me.updateSelectionMode(obj, old);
          return true;
        }
      });
    }
    /**
     * The selectionMode configuration has been changed.
     * @event selectionModeChange
     * @param {Object} selectionMode The new {@link #config-selectionMode}
     */
    // Will be called if selectionMode config object changes or if one of its properties changes
    updateSelectionMode(mode, oldMode = this.$defaultGridSelection) {
      var _a6;
      const me = this, {
        columns,
        checkboxSelectionColumn
      } = me, changed = {}, { rowReorder } = me.features;
      for (const property in mode) {
        if (mode[property] != oldMode[property]) {
          changed[property] = mode[property];
        }
      }
      if (changed.column) {
        mode.cell = true;
        mode.multiSelect = true;
      }
      if (changed.cell) {
        mode.checkboxOnly = false;
      }
      if (changed.cell === false) {
        mode.column = false;
      }
      if (changed.alwaysMultiSelect) {
        mode.multiSelect = true;
        mode.deselectOnClick = true;
      }
      if (changed.checkboxOnly) {
        if (!mode.checkbox) {
          mode.checkbox = true;
        }
        mode.cell = false;
      }
      if (changed.checkbox === false) {
        changed.checkboxOnly = false;
        changed.showCheckAll = false;
      }
      if (changed.showCheckAll) {
        mode.checkbox = mode.checkbox || true;
        mode.multiSelect = true;
      }
      if (changed.includeChildren || changed.includeParents || changed.showCheckAllInGroupRows) {
        mode.multiSelect = true;
        if (me.features.group) {
          mode.includeParents = mode.showCheckAllInGroupRows;
        }
      }
      if (mode.checkbox && ((_a6 = me.features.group) == null ? void 0 : _a6.enabled) && typeof changed.showCheckAllInGroupRows === "boolean") {
        me.renderRows();
      }
      if (changed.multiSelect === false) {
        mode.column = mode.showCheckAll = mode.dragSelect = mode.includeChildren = mode.includeParents = mode.alwaysMultiSelect = mode.showCheckAllInGroupRows = false;
      }
      if (changed.deselectOnClick === false) {
        mode.alwaysMultiSelect = false;
      }
      if (changed.dragSelect) {
        if ((rowReorder == null ? void 0 : rowReorder.enabled) && rowReorder.gripOnly !== true) {
          rowReorder.showGrip = rowReorder.gripOnly = true;
        }
        mode.multiSelect = true;
        me._selectionListenersDetachers = {};
      }
      if (changed.dragSelect === false && me._selectionListenersDetachers) {
        me.removeDragListeners();
      }
      if (!me.isConfiguring && oldMode && (changed.cell !== void 0 || changed.deselectFilteredOutRecords !== void 0 || changed.multiSelect !== void 0)) {
        me.deselectAll();
      }
      if (changed.rowNumber) {
        if (!columns.findRecord("type", "rownumber")) {
          const defaults = me.isGanttBase ? { type: "sequence", text: "" } : { type: "rownumber" };
          columns.insert(0, {
            ...defaults,
            ...typeof mode.rowNumber == "object" ? mode.rowNumber : {}
          });
          me._selectionAddedRowNumberColumn = true;
        }
      } else if (changed.rowNumber === false && me._selectionAddedRowNumberColumn) {
        columns.remove(columns.findRecord("type", "rownumber"));
        delete me._selectionAddedRowNumberColumn;
      }
      if (mode.checkbox !== (oldMode == null ? void 0 : oldMode.checkbox) || mode.checkbox && mode.showCheckAll !== (oldMode == null ? void 0 : oldMode.showCheckAll)) {
        if (me.isConfiguring) {
          me.shouldInitCheckboxSelection = true;
        } else {
          if (oldMode) {
            me.deselectAll();
          }
          me.initCheckboxSelection();
        }
      }
      if (oldMode && mode.checkbox && oldMode.checkbox && mode.checkboxIndex !== oldMode.checkboxIndex && checkboxSelectionColumn) {
        checkboxSelectionColumn.parent.insertChild(checkboxSelectionColumn, columns.getAt(me.checkboxSelectionColumnInsertIndex));
      }
      me.trigger("selectionModeChange", ObjectHelper.clone(mode));
      me.afterSelectionModeChange(mode);
    }
    afterConfigure() {
      const me = this;
      if (me.shouldInitCheckboxSelection) {
        me.shouldInitCheckboxSelection = false;
        me.initCheckboxSelection();
      }
      me.scrollManager.ion({
        scroll: ({ event, deltaY }) => {
          if (deltaY !== 0) {
            me.onSelectionDrag(event);
          }
        }
      });
      super.afterConfigure();
    }
    initCheckboxSelection() {
      var _a6, _b2, _c2;
      const me = this, {
        selectionMode,
        columns,
        checkboxSelectionColumn
      } = me, { checkbox } = selectionMode;
      if (checkboxSelectionColumn) {
        me.checkboxSelectionColumn = null;
        columns.remove(checkboxSelectionColumn);
        checkboxSelectionColumn.destroy();
      }
      if (checkbox) {
        const checkColumnClass = ColumnStore.getColumnClass("check"), config = checkbox === true ? null : checkbox;
        if (!checkColumnClass) {
          throw new Error("CheckColumn must be imported for checkbox selection mode to work");
        }
        const col = me.checkboxSelectionColumn = new checkColumnClass(ObjectHelper.assign({
          id: `${me.id}-selection-column`,
          width: ((_a6 = me.features.rowReorder) == null ? void 0 : _a6.showGrip) ? "5em" : "4em",
          field: null,
          sortable: false,
          filterable: false,
          groupable: false,
          hideable: false,
          cellCls: "b-checkbox-selection",
          // Always put the checkcolumn in the first region
          region: (_c2 = (_b2 = me.items) == null ? void 0 : _b2[0]) == null ? void 0 : _c2.region,
          showCheckAll: selectionMode.showCheckAll,
          draggable: false,
          resizable: false,
          ariaLabel: me.L("L{Checkbox.toggleSelection}"),
          widgets: [{
            type: "checkbox",
            valueProperty: "checked",
            ariaLabel: "L{Checkbox.toggleRowSelect}"
          }]
        }, config), columns, { isSelectionColumn: true });
        col.meta.depth = 0;
        col._grid = me;
        const checkboxRenderer = col.renderer;
        col.setData("renderer", (renderData) => {
          renderData.value = me.isSelected(renderData.record);
          checkboxRenderer.call(col, renderData);
        });
        col.ion({
          toggle: "onCheckChange",
          toggleAll: "onCheckAllChange",
          thisObj: me
        });
        columns.insert(me.checkboxSelectionColumnInsertIndex, col);
        columns.acceptChanges();
      }
    }
    // Used internally to get the index where to insert checkboxselectioncolumn
    // Default : Insert the checkbox after any rownumber column. If not there, -1 means in at 0.
    // If provided, insert at provided index
    get checkboxSelectionColumnInsertIndex() {
      const { columns } = this;
      let { checkboxIndex } = this.selectionMode;
      if (!checkboxIndex) {
        checkboxIndex = columns.indexOf(columns.findRecord("type", "rownumber")) + 1;
      } else if (typeof checkboxIndex === "string") {
        checkboxIndex = columns.indexOf(columns.getById(checkboxIndex));
      }
      return checkboxIndex;
    }
    beforeColumnsChange() {
      if (this.columns.count === 0) {
        this.deselectAll(false, true);
      }
    }
    afterColumnsChange({ type, action, changes, columns, column }) {
      var _a6;
      const { _selectedCells } = this;
      if (_selectedCells == null ? void 0 : _selectedCells.length) {
        if (!columns && column) {
          columns = [column];
        }
        if (type === "move" || (changes == null ? void 0 : changes.hidden) || (changes == null ? void 0 : changes.region) || action === "add" || action === "remove") {
          let selectedCells = _selectedCells.map((c) => ({ id: c.id, columnId: c._column.id }));
          if ((action === "remove" || ((_a6 = changes == null ? void 0 : changes.hidden) == null ? void 0 : _a6.value)) && (columns == null ? void 0 : columns.length)) {
            selectedCells = selectedCells.filter((cell) => !columns.some((column2) => cell.columnId === column2.id));
          }
          this.deselectAll(false, true);
          this.selectCells({ cells: selectedCells, scrollIntoView: false });
        }
      }
    }
    //endregion
    // region Events docs & Hooks
    /**
     * The selection has been changed.
     * @event selectionChange
     * @param {'select'|'deselect'} action `'select'`/`'deselect'`
     * @param {'row'|'cell'} mode `'row'`/`'cell'`
     * @param {Grid.view.Grid} source
     * @param {Core.data.Model[]} deselected The records deselected in this operation.
     * @param {Core.data.Model[]} selected The records selected in this operation.
     * @param {Core.data.Model[]} selection The records in the new selection.
     * @param {Grid.util.GridLocation[]} deselectedCells The cells deselected in this operation.
     * @param {Grid.util.GridLocation[]} selectedCells The cells selected in this operation.
     * @param {Grid.util.GridLocation[]} cellSelection The cells in the new selection.
     */
    /**
     * Fires before the selection changes. Returning `false` from a listener prevents the change
     * @event beforeSelectionChange
     * @preventable
     * @param {String} action `'select'`/`'deselect'`
     * @param {'row'|'cell'} mode `'row'`/`'cell'`
     * @param {Grid.view.Grid} source
     * @param {Core.data.Model[]} deselected The records to be deselected in this operation.
     * @param {Core.data.Model[]} selected The records to be selected in this operation.
     * @param {Core.data.Model[]} selection The records in the current selection, before applying `selected` and
     * `deselected`
     * @param {Grid.util.GridLocation[]} deselectedCells The cells to be deselected in this operation.
     * @param {Grid.util.GridLocation[]} selectedCells The cells to be selected in this operation.
     * @param {Grid.util.GridLocation[]} cellSelection  The cells in the current selection, before applying `selectedCells`
     * and `deselectedCells`
     */
    afterSelectionChange() {
    }
    afterSelectionModeChange() {
    }
    // endregion
    // region selectedRecordCollection
    changeSelectedRecordCollection(collection) {
      if (collection == null ? void 0 : collection.isCollection) {
        if (!collection.owner) {
          collection.owner = this;
        }
        return collection;
      }
      return Collection.new(collection, { owner: this });
    }
    updateSelectedRecordCollection(collection) {
      collection.ion({
        change: "onSelectedRecordCollectionChange",
        thisObj: this
      });
    }
    onSelectedRecordCollectionChange({ added = [], removed }) {
      if (this.selectedRecordCollection._fromSelection !== this) {
        added = added.filter((row) => this.isSelectable(row));
        this.performSelection({
          selectedCells: [],
          deselectedCells: [],
          selectedRecords: added,
          deselectedRecords: removed
        });
      }
    }
    changeSelectedRecordCollectionSilent(fn2) {
      this.selectedRecordCollection._fromSelection = this;
      const result = fn2(this.selectedRecordCollection);
      delete this.selectedRecordCollection._fromSelection;
      return result;
    }
    // endregion
    // region Store
    bindStore(store) {
      var _a6;
      this.detachListeners("selectionStoreFilter");
      store.ion({
        name: "selectionStoreFilter",
        filter: "onStoreFilter",
        thisObj: this
      });
      (_a6 = super.bindStore) == null ? void 0 : _a6.call(this, store);
    }
    unbindStore(oldStore) {
      this.detachListeners("selectionStoreFilter");
      super.unbindStore(oldStore);
    }
    onStoreFilter({ source }) {
      const me = this, deselect = me.selectedRows.filter((record) => !(source.indexOf(record, false, true) > -1)), selectionChange = me.prepareSelection(me.selectionMode.deselectFilteredOutRecords ? deselect : []);
      if (me.isCellSelectionMode) {
        const { deselectedCells } = me.prepareSelection(me._selectedCells.filter((cell) => !source.records.includes(cell.record)));
        if (deselectedCells == null ? void 0 : deselectedCells.length) {
          selectionChange.deselectedCells = (selectionChange.deselectedCells || []).concat(deselectedCells);
        }
      }
      if (selectionChange.deselectedCells.length || selectionChange.deselectedRecords.length) {
        me.performSelection(selectionChange, false);
        me.updateCheckboxHeader();
      }
    }
    /**
     * Triggered from Grid view when the id of a record has changed.
     * Update the collection indices.
     * @private
     * @category Selection
     */
    onStoreRecordIdChange({ record, oldValue }) {
      var _a6;
      (_a6 = super.onStoreRecordIdChange) == null ? void 0 : _a6.call(this, ...arguments);
      const item = this.selectedRecordCollection.get(oldValue);
      if (item === record) {
        this.selectedRecordCollection.rebuildIndices();
      }
    }
    /**
     * Triggered from Grid view when records get removed from the store.
     * Deselects all records which have been removed.
     * @private
     * @category Selection
     */
    onStoreRemove({ action, records, isCollapse }) {
      var _a6;
      (_a6 = super.onStoreRemove) == null ? void 0 : _a6.call(this, ...arguments);
      if (!isCollapse && action !== "filter") {
        const me = this, deselectedRecords = records.filter((rec) => me.isSelected(rec)), selectionChange = me.prepareSelection(deselectedRecords);
        if (me.isCellSelectionMode) {
          const { deselectedCells } = me.prepareSelection(me.getSelectedCellsForRecords(records));
          if (deselectedCells == null ? void 0 : deselectedCells.length) {
            selectionChange.deselectedCells = (selectionChange.deselectedCells || []).concat(deselectedCells);
          }
        }
        if (deselectedRecords.length || selectionChange.deselectedCells.length) {
          me.performSelection(selectionChange);
        }
      }
    }
    /**
     * Triggered from Grid view when the store changes. This might happen
     * if store events are batched and then resumed.
     * Deselects all records which have been removed.
     * @private
     * @category Selection
     */
    onStoreDataChange({ action, source: store }) {
      var _a6;
      const me = this, { selectionMode } = me;
      let selectionChange;
      (_a6 = super.onStoreDataChange) == null ? void 0 : _a6.call(this, ...arguments);
      if (action === "pageLoad") {
        if (!selectionMode.preserveSelectionOnPageChange) {
          selectionChange = me.prepareSelection(null, null, true);
        }
        me.updateCheckboxHeader();
      } else if (isDataLoadAction[action]) {
        const deselect = [];
        if (selectionMode.preserveSelectionOnDatasetChange === false) {
          selectionChange = me.prepareSelection(null, null, true);
        } else {
          deselect.push(...me.changeSelectedRecordCollectionSilent((c) => c.match(store.storage)));
          for (const selectedCell of me._selectedCells) {
            if (!store.getById(selectedCell.id)) {
              deselect.push(selectedCell);
            }
          }
          selectionChange = me.prepareSelection(deselect);
        }
      }
      if (selectionChange && (selectionChange.deselectAll || selectionChange.deselectedCells.length || selectionChange.deselectedRecords.length || selectionChange.selectedCells.length || selectionChange.selectedRecords.length)) {
        me.performSelection(selectionChange, false);
        me.updateCheckboxHeader();
      }
    }
    /**
     * Triggered from Grid view when all records get removed from the store.
     * Deselects all records.
     * @private
     * @category Selection
     */
    onStoreRemoveAll() {
      var _a6;
      (_a6 = super.onStoreRemoveAll) == null ? void 0 : _a6.call(this);
      this.performSelection(this.prepareSelection(null, null, true), false);
    }
    //endregion
    // region Checkbox selection
    onCheckChange({ checked, record, checkbox }) {
      const me = this, { multiSelect } = me.selectionMode, deselectAll = !multiSelect && checked, deselect = !deselectAll && !checked ? [record] : null, select = checked ? [record] : null;
      me._isCheckboxSelecting = true;
      if (record.isGroupHeader && me.selectionMode.showCheckAllInGroupRows) {
        (checked ? select : deselect).splice(0, 1, ...record.groupChildren);
      }
      if (checked && !GlobalEvents_default.shiftKeyDown) {
        me._lastSelectionChecked = record;
      }
      if (checked && multiSelect && me._lastSelectionChecked && GlobalEvents_default.shiftKeyDown) {
        me.performSelection(me.internalSelectRange(me._lastSelectionChecked, record, true));
      } else if (me.performSelection(me.prepareSelection(deselect, select, deselectAll, true)) === false) {
        checkbox.checked = !checkbox.checked;
      }
      me._isCheckboxSelecting = false;
    }
    // Update header checkbox
    updateCheckboxHeader() {
      var _a6;
      const me = this, {
        selectionMode,
        checkboxSelectionColumn,
        store
      } = me;
      if (selectionMode.checkbox && checkboxSelectionColumn) {
        if (!me.hideHeaders && !me._isCheckAllSelecting && selectionMode.showCheckAll && checkboxSelectionColumn.headerCheckbox) {
          const allSelected = me.isAllRecordsSelected();
          if (checkboxSelectionColumn.headerCheckbox.checked !== allSelected) {
            checkboxSelectionColumn.suspendEvents();
            checkboxSelectionColumn.headerCheckbox.checked = allSelected;
            checkboxSelectionColumn.resumeEvents();
          }
        }
        if (selectionMode.showCheckAllInGroupRows && ((_a6 = store.groupRecords) == null ? void 0 : _a6.count)) {
          const fullySelectedGroups = [...new Set(me.selectedRecords.map((r) => r.instanceMeta(store).groupParent))].filter((g) => {
            var _a7;
            return (_a7 = g == null ? void 0 : g.groupChildren) == null ? void 0 : _a7.every((c) => me.isSelected(c));
          });
          checkboxSelectionColumn.suspendEvents();
          for (const groupRecord of store.groupRecords) {
            const cell = me.getCell({ record: groupRecord, column: checkboxSelectionColumn });
            if (cell) {
              cell.widget.checked = fullySelectedGroups.includes(groupRecord);
            }
          }
          checkboxSelectionColumn.resumeEvents();
        }
      }
    }
    // Overridden in Split.js
    isAllRecordsSelected() {
      return this.store.count && !this.store.some((record) => this.isSelectable(record) && !this.isSelected(record));
    }
    onCheckAllChange({ checked }) {
      const me = this;
      me._isCheckboxSelecting = me._isCheckAllSelecting = true;
      me[checked ? "selectAll" : "deselectAll"](me.store.isPaged && me.selectionMode.preserveSelectionOnPageChange);
      me._isCheckboxSelecting = me._isCheckAllSelecting = false;
    }
    //endregion
    // region Selection drag
    // Hook for SalesForce-code to overwrite
    get selectionDragMouseEventListenerElement() {
      return globalThis;
    }
    // Creates new selection range on mouseover. Listener is initiated on mousedown
    onSelectionDrag(event) {
      var _a6;
      const me = this, isScroll = event.type === "scroll", { _selectionStartCell, lastPointerMoveEvent } = me;
      if (isScroll && !me._isSelectionDragging) {
        return;
      }
      if (!GlobalEvents_default.isMouseDown()) {
        me.onSelectionEnd();
      }
      if (!_selectionStartCell) {
        return;
      }
      const { items: items2, _lastSelectionDragRegion } = me;
      let cellData, region, cellSelector;
      if (isScroll && lastPointerMoveEvent) {
        const el = document.elementFromPoint(lastPointerMoveEvent.clientX, lastPointerMoveEvent.clientY), cellEl = el == null ? void 0 : el.closest(".b-grid-cell");
        if (cellEl) {
          region = me._lastSelectionDragRegion;
          cellSelector = me.normalizeCellContext({
            columnId: cellEl.dataset.columnId,
            record: me.store.getById(cellEl.parentElement.dataset.id)
          });
        }
      } else {
        cellData = me.getCellDataFromEvent(event, true);
        region = cellData == null ? void 0 : cellData.column.region;
        cellSelector = (cellData == null ? void 0 : cellData.cellSelector) && me.normalizeCellContext(cellData.cellSelector);
        if (cellData) {
          me.lastPointerMoveEvent = event;
        }
      }
      if (cellSelector && !cellSelector.equals(me._lastSelectionDragCell, true)) {
        if (!me._isSelectionDragging) {
          me.enableScrollingCloseToEdges(items2);
        }
        if (me._clearSelectionOnSelectionDrag && !_selectionStartCell.equals(cellSelector, true)) {
          me.deselectAll();
          delete me._clearSelectionOnSelectionDrag;
        }
        if (_lastSelectionDragRegion && region !== _lastSelectionDragRegion) {
          const leavingSubGrid = me.subGrids[_lastSelectionDragRegion], enteringSubGrid = me.subGrids[region], leavingScrollable = leavingSubGrid.scrollable, enteringScrollable = enteringSubGrid.scrollable, goingForward = items2.indexOf(leavingSubGrid) - items2.indexOf(enteringSubGrid) < 0;
          enteringScrollable.x = goingForward ? 0 : enteringScrollable.maxX;
          if (goingForward ? leavingScrollable.x < leavingScrollable.maxX - 1 : leavingScrollable.x > 1) {
            return;
          }
          const activeHorizontalScroll = (_a6 = me.scrollManager._activeScroll) == null ? void 0 : _a6.horizontal;
          if (activeHorizontalScroll && activeHorizontalScroll.element !== enteringScrollable.element) {
            activeHorizontalScroll.stopScroll(true);
          }
        }
        me._lastSelectionDragRegion = region;
        me._lastSelectionDragCell = cellSelector;
        me._isSelectionDragging = true;
        const selectionChange = me._lastSelectionDragChange = me.internalSelectRange(
          _selectionStartCell,
          cellSelector,
          me.isRowNumberSelecting(cellSelector) || me.isRowNumberSelecting(_selectionStartCell)
        );
        selectionChange.deselectedCells = selectionChange.deselectedCells.filter((cell) => !me.isCellSelected(cell));
        selectionChange.deselectedRecords = selectionChange.deselectedRecords.filter((record) => !me.isSelected(record));
        me.hoveredCell = null;
        me.refreshGridSelectionUI(selectionChange);
        me.trigger("dragSelecting", selectionChange);
      }
    }
    // Tells onSelectionDrag that it's not dragging any longer
    onSelectionEnd() {
      const me = this, lastChange = me._lastSelectionDragChange;
      if (me._isSelectionDragging && !me._selectionStartCell.equals(me._lastSelectionDragCell, true) && lastChange) {
        me.performSelection(lastChange, false);
      }
      me.disableScrollingCloseToEdges(me.items);
      me._isSelectionDragging = false;
      me._lastSelectionDragChange = me._lastSelectionDragCell = me._lastSelectionDragRegion = null;
      me.removeDragListeners();
    }
    removeDragListeners() {
      var _a6;
      const { _selectionListenersDetachers } = this;
      if (_selectionListenersDetachers) {
        (_a6 = _selectionListenersDetachers.selectiondrag) == null ? void 0 : _a6.call(_selectionListenersDetachers);
        delete this._selectionListenersDetachers.selectiondrag;
      }
    }
    // endregion
    // region Column selection
    onHandleElementClick(event) {
      const me = this;
      if (me.selectionMode.rowNumber && event.target.closest(".b-rownumber-header")) {
        event.handled = true;
        if (me.store.count && me.store.some((record) => !me.isSelected(record))) {
          me.selectAll();
        } else {
          me.deselectAll();
        }
      } else if (me.selectionMode.column && event.target.matches(".b-grid-header-text-content,.b-grid-header-text")) {
        event.handled = true;
        me.selectColumn(event, event.ctrlKey);
      }
      super.onHandleElementClick(event);
    }
    selectColumn(event, addToSelection = false) {
      const me = this, { store } = me, { columnId } = me.getHeaderDataFromEvent(event);
      if (store.count === 0) {
        return;
      }
      me._shiftSelectRange = null;
      if (!event.shiftKey) {
        me._shiftSelectColumn = columnId;
      }
      const fromColumnId = event.shiftKey && me._shiftSelectColumn || columnId, selectionChange = me.internalSelectRange(
        me.normalizeCellContext({ id: store.first.id, columnId: fromColumnId }),
        me.normalizeCellContext({ id: store.last.id, columnId })
      );
      if (addToSelection && !selectionChange.selectedCells.some((sc) => !me.isCellSelected(sc))) {
        selectionChange.deselectedCells = selectionChange.selectedCells;
        selectionChange.selectedCells = [];
      }
      if (!addToSelection) {
        selectionChange.deselectedCells = me._selectedCells;
      }
      me.cleanSelectionChange(selectionChange);
      me.performSelection(selectionChange);
    }
    // endregion
    // region Public row/record selection
    /**
     * Checks whether a row is selected. Will not check if any of a row's cells are selected.
     * @param {GridLocationConfig|Grid.util.GridLocation|String|Number|Core.data.Model} cellSelectorOrId Cell selector { id: x, column: xx } or row
     * id, or record
     * @returns {Boolean} true if row is selected, otherwise false
     * @category Selection
     */
    isSelected(cellSelectorOrId) {
      if (this.selectionMode.showCheckAllInGroupRows && (cellSelectorOrId == null ? void 0 : cellSelectorOrId.isGroupHeader)) {
        return cellSelectorOrId.groupChildren.every((rec) => this.isSelected(rec));
      }
      if (cellSelectorOrId == null ? void 0 : cellSelectorOrId.id) {
        cellSelectorOrId = cellSelectorOrId.id;
      }
      if (validIdTypes[typeof cellSelectorOrId]) {
        return this.selectedRows.some((rec) => rec.id === cellSelectorOrId);
      }
      return false;
    }
    /**
     * Checks whether a cell is selected.
     * @param {GridLocationConfig|Grid.util.GridLocation} cellSelector Cell selector { id: x, column: xx }
     * @param {Boolean} includeRow to also check if row is selected
     * @returns {Boolean} true if cell is selected, otherwise false
     * @category Selection
     */
    isCellSelected(cellSelector, includeRow) {
      cellSelector = this.normalizeCellContext(cellSelector);
      return this.isCellSelectionMode && this._selectedCells.some((cell) => cellSelector.equals(cell, true)) || includeRow && this.isSelected(cellSelector);
    }
    /**
     * Checks whether a cell or row can be selected.
     * @param {Core.data.Model|GridLocationConfig|Grid.util.GridLocation|String|Number} recordCellOrId Record or cell or record id
     * @returns {Boolean} true if cell or row can be selected, otherwise false
     * @category Selection
     */
    isSelectable(recordCellOrId) {
      return this.normalizeCellContext({ id: recordCellOrId.id || recordCellOrId }).isSelectable;
    }
    /**
     * The last selected record. Set to select a row or use Grid#selectRow. Set to null to
     * deselect all
     * @property {Core.data.Model}
     * @category Selection
     */
    get selectedRecord() {
      return this.selectedRecords[this.selectedRecords.length - 1] || null;
    }
    set selectedRecord(record) {
      this.selectRow({ record });
    }
    /**
     * Selected records.
     *
     * If {@link #config-selectionMode deselectFilteredOutRecords} is `false` (default) this will include selected
     * records which has been filtered out.
     *
     * If {@link #config-selectionMode preserveSelectionOnPageChange} is `true` (defaults to `false`) this will include
     * selected records on all pages.
     *
     * If {@link #config-selectionMode selectRecordOnCell} is `true` (default) this will include any record which has at
     * least one cell selected.
     *
     * Can be set as array of ids:
     *
     * ```javascript
     * grid.selectedRecords = [1, 2, 4, 6]
     * ```
     *
     * @property {Core.data.Model[]}
     * @accepts {Core.data.Model[]|Number[]}
     * @category Selection
     */
    get selectedRecords() {
      return this.selectedRecordCollection.values;
    }
    set selectedRecords(selectedRecords) {
      this.selectRows(selectedRecords);
    }
    /**
     * Selected records. Records selected via cell selection is excluded.
     *
     * If {@link #config-selectionMode deselectFilteredOutRecords} is `false` (default) this will include selected
     * records which has been filtered out.
     *
     * If {@link #config-selectionMode preserveSelectionOnPageChange} is `true` (defaults to `false`) this will include
     * selected records on all pages.
     *
     * if {@link #config-selectionMode selectRecordOnCell} is `false` this will return same records as
     * {@link #property-selectedRecords}.
     *
     * Can be set as array of ids:
     *
     * ```javascript
     * grid.selectedRecords = [1, 2, 4, 6]
     * ```
     *
     * @property {Core.data.Model[]}
     * @accepts {Core.data.Model[]|Number[]}
     * @category Selection
     */
    get selectedRows() {
      return [...this._selectedRows];
    }
    set selectedRows(selectedRows) {
      this.selectRows(selectedRows);
    }
    /**
     * Removes and adds records to/from the selection at the same time. Analogous to the `Array` `splice` method.
     *
     * Note that if records that are specified for removal are also in the `toAdd` array, then those records are *not*
     * removed and then appended. They remain in the same position relative to all remaining records.
     *
     * @param {Number} index Index at which to remove a block of records. Only valid if the second, `toRemove` argument
     * is a number.
     * @param {Core.data.Model[]|Number} toRemove Either the number of records to remove starting at the passed `index`,
     * or an array of records to remove (If an array is passed, the `index` is ignored).
     * @param  {Core.data.Model[]|Core.data.Model} toAdd A record, or an array of records to add.
     * @category Selection
     */
    spliceSelectedRecords(index, toRemove, toAdd) {
      const me = this;
      if (typeof toRemove == "number") {
        const select = [...me.selectedRecords];
        select.splice(index, toRemove, ...ArrayHelper.asArray(toAdd));
        me.performSelection(me.prepareSelection(null, select, true, true));
      } else {
        me.performSelection(me.prepareSelection(toRemove, toAdd, false, true));
      }
    }
    /**
     * Select one row
     * @param {Object|Core.data.Model|String|Number} options A record or id to select or a config object describing the
     * selection
     * @param {Core.data.Model|String|Number} options.record Record or record id, specifying null will deselect all
     * @param {Grid.column.Column} [options.column] The column to scroll into view if `scrollIntoView` is not specified as
     * `false`. Defaults to the grid's first column.
     * @param {Boolean|BryntumScrollOptions} [options.scrollIntoView] Specify `false` to prevent row from being scrolled into view
     * @param {Boolean} [options.addToSelection] Specify `true` to add to selection, defaults to `false` which replaces
     * @fires selectionChange
     * @category Selection
     */
    selectRow(options) {
      if (typeof options === "number" || typeof options === "string" || options.isModel || !("record" in options)) {
        options = {
          records: [options]
        };
      }
      ObjectHelper.assignIf(options, {
        scrollIntoView: true
      });
      this.selectRows(options);
    }
    /**
     * Select one or more rows
     * @param {Object|Core.data.Model[]|String[]|Number[]} options An array of records or ids for a record or a
     * config object describing the selection
     * @param {Core.data.Model[]|String[]|Number[]} options.records An array of records or ids for a record
     * @param {Grid.column.Column} options.column The column to scroll into view if `scrollIntoView` is not specified as
     * `false`. Defaults to the grid's first column.
     * @param {Boolean|BryntumScrollOptions} [options.scrollIntoView] Specify `false` to prevent row from being scrolled into view
     * @param {Boolean} [options.addToSelection] Specify `true` to add to selection, defaults to `false` which replaces
     * @category Selection
     */
    selectRows(options) {
      if (!options || Array.isArray(options) || options.isModel || typeof options === "number" || !("records" in options) && !("record" in options)) {
        options = {
          records: ArrayHelper.asArray(options) || []
        };
      }
      const me = this, { store } = me, toSelect = [], {
        records = options.record ? [options.record] : [],
        // Got a record instead of records
        column = me.columns.visibleColumns[0],
        // Default
        scrollIntoView,
        addToSelection = arguments[1]
        // Backwards compatibility
      } = options;
      for (let record of records) {
        record = store.getById(record);
        if (record) {
          toSelect.push(record);
        }
      }
      if (!addToSelection) {
        me._shiftSelectRange = null;
      }
      me.performSelection(me.prepareSelection(null, toSelect, !addToSelection, true));
      if (toSelect.length && scrollIntoView) {
        return me.scrollRowIntoView(toSelect[0].id, {
          ...scrollIntoView,
          column
        });
      }
    }
    /**
     * This selects all rows. If store is filtered, this will merge the selection of all visible rows with any selection
     * made prior to filtering.
     * @privateparam {Boolean} [silent] Pass `true` not to fire any event upon selection change
     * @category Selection
     * @non-lazy-load
     */
    selectAll(silent = false) {
      const { store } = this, records = (store.isGrouped ? store.getAllDataRecords() : store.records).filter((r) => !r.isSpecialRow);
      this.performSelection(this.prepareSelection(null, records, false, true), true, silent);
    }
    /**
     * Deselects all selected rows and cells. If store is filtered, this will unselect all visible rows only. Any
     * selections made prior to filtering remains.
     * @param {Boolean} [removeCurrentRecordsOnly] Pass `false` to clear all selected records, and `true` to only
     * clear selected records in the current set of records
     * @param {Boolean} [silent] Pass `true` not to fire any event upon selection change
     * @category Selection
     */
    deselectAll(removeCurrentRecordsOnly = false, silent = false) {
      const { store } = this, records = removeCurrentRecordsOnly ? (store.isGrouped ? store.getAllDataRecords() : store.records).filter((r) => !r.isSpecialRow) : null;
      this.performSelection(this.prepareSelection(records, null, !removeCurrentRecordsOnly), true, silent);
    }
    /**
     * Deselect one row
     * @param {Core.data.Model|String|Number} recordOrId Record or an id for a record
     * @category Selection
     */
    deselectRow(record) {
      this.deselectRows(record);
    }
    /**
     * Deselect one or more rows
     * @param {Core.data.Model|String|Number|Core.data.Model[]|String[]|Number[]} recordOrIds An array of records or ids
     * for a record
     * @category Selection
     */
    deselectRows(recordsOrIds) {
      const { store } = this, records = ArrayHelper.asArray(recordsOrIds).map((recordOrId) => store.getById(recordOrId)).filter((rec) => rec);
      this.performSelection(this.prepareSelection(records));
    }
    /**
     * Selects rows corresponding to a range of records (from fromId to toId)
     * @param {String|Number} fromId
     * @param {String|Number} toId
     * @category Selection
     */
    selectRange(fromId, toId, addToSelection = false) {
      const me = this, { store } = me, selection = me.internalSelectRange(store.getById(fromId), store.getById(toId), true);
      me._shiftSelectRange = null;
      me.performSelection(selection);
    }
    // endregion
    // region Public cell selection
    /**
     * In cell selection mode, this will get the cell selector for the (last) selected cell. Set to an available cell
     * selector to select only that cell. Or use {@link #function-selectCell()} instead.
     * @property {Grid.util.GridLocation}
     * @accepts {Grid.util.GridLocation|GridLocationConfig}
     * @category Selection
     */
    get selectedCell() {
      return this._selectedCells[this._selectedCells.length - 1];
    }
    set selectedCell(cellSelector) {
      this.selectCells([cellSelector]);
    }
    /**
     * In cell selection mode, this will get the cell selectors for all selected cells. Set to an array of available
     * cell selectors. Or use {@link #function-selectCells()} instead.
     * @property {Grid.util.GridLocation[]}
     * @accepts {Grid.util.GridLocation[]|GridLocationConfig[]}
     * @category Selection
     */
    get selectedCells() {
      return [...this._selectedCells];
    }
    set selectedCells(cellSelectors) {
      this.selectCells(cellSelectors);
    }
    /**
     * CSS selector for the currently selected cell. Format is "[data-index=index] [data-column-id=column]".
     * @type {String}
     * @category Selection
     * @readonly
     */
    get selectedCellCSSSelector() {
      const cell = this.selectedCell, row = cell && this.getRowById(cell.id);
      if (!cell || !row)
        return "";
      return `[data-index=${row.dataIndex}] [data-column-id=${cell.columnId}]`;
    }
    /**
     * If in cell selection mode, this selects one cell. If not, this selects the cell's record.
     * @param {GridLocationConfig|Grid.util.GridLocation|Object} options A cell selector ({ id: rowId, columnId: 'columnId' }) or a config object
     * @param {GridLocationConfig|Grid.util.GridLocation} options.cell  A cell selector ({ id: rowId, columnId: 'columnId' })
     * @param {Boolean|BryntumScrollOptions} [options.scrollIntoView] Specify `false` to prevent row from being scrolled into view
     * @param {Boolean} [options.addToSelection] Specify `true` to add to selection, defaults to `false` which replaces
     * @param {Boolean} [options.silent] Specify `true` to not trigger any events when selecting the cell
     * @returns {Grid.util.GridLocation} Cell selector
     * @fires selectionChange
     * @category Selection
     */
    selectCell(options) {
      var _a6;
      if ("id" in options || "row" in options || "record" in options || "rowIndex" in options) {
        options = {
          cell: options
        };
        options = Object.assign({
          scrollIntoView: arguments[1],
          addToSelection: arguments[2],
          silent: arguments[3]
        }, options);
      }
      return (_a6 = this.selectCells(options)) == null ? void 0 : _a6[0];
    }
    /**
     * If in cell selection mode, this selects a number of cells. If not, this selects corresponding records.
     * @param {Object|GridLocationConfig[]|Grid.util.GridLocation[]} options An array of cell selectors ({ id: rowId, columnId: 'columnId' }) or a config
     * object
     * @param {GridLocationConfig[]|Grid.util.GridLocation[]} options.cells An array of cell selectors { id: rowId, columnId: 'columnId' }
     * @param {Boolean|BryntumScrollOptions} [options.scrollIntoView] Specify `false` to prevent row from being scrolled into view
     * @param {Boolean} [options.addToSelection] Specify `true` to add to selection, defaults to `false` which replaces
     * @param {Boolean} [options.silent] Specify `true` to not trigger any events when selecting the cell
     * @returns {Grid.util.GridLocation[]} Cell selectors
     * @fires selectionChange
     * @category Selection
     */
    selectCells(options) {
      if (Array.isArray(options)) {
        options = {
          cells: options
        };
      }
      const me = this, {
        cells = options.cell ? [options.cell] : [],
        // Got a cell instead of cells
        scrollIntoView = true,
        addToSelection = false,
        silent = false
      } = options, selectionChange = me.prepareSelection(null, cells, !addToSelection);
      if (!addToSelection) {
        me._shiftSelectRange = null;
      }
      me.performSelection(selectionChange, true, silent);
      if (scrollIntoView) {
        me.scrollRowIntoView(cells[0].id, {
          ...scrollIntoView,
          column: cells[0].columnId
        });
      }
      return me.isCellSelectionMode ? selectionChange.selectedCells : selectionChange.selectedRecords;
    }
    /**
     * If in cell selection mode, this deselects one cell. If not, this deselects the cell's record.
     * @param {GridLocationConfig|Grid.util.GridLocation} cellSelector
     * @returns {Grid.util.GridLocation} Normalized cell selector
     * @category Selection
     */
    deselectCell(cellSelector) {
      var _a6;
      return (_a6 = this.deselectCells([cellSelector])) == null ? void 0 : _a6[0];
    }
    /**
     * If in cell selection mode, this deselects a number of cells. If not, this deselects corresponding records.
     * @param {GridLocationConfig[]|Grid.util.GridLocation[]} cellSelectors
     * @returns {Grid.util.GridLocation[]} Normalized cell selectors
     * @category Selection
     */
    deselectCells(cellSelectors) {
      const selectionChange = this.prepareSelection(cellSelectors);
      this.performSelection(selectionChange);
      return this.isCellSelectionMode ? selectionChange.deselectedCells : selectionChange.deselectedRecords;
    }
    // Used by keymap to toggle selection of currently focused cell.
    toggleSelection(keyEvent) {
      const me = this, {
        _focusedCell,
        selectionMode
      } = me, isRowNumber = me.isRowNumberSelecting(_focusedCell), isSelected = me.isCellSelected(_focusedCell, true);
      if (selectionMode.selectOnKeyboardNavigation === true || _focusedCell.isActionable) {
        return false;
      }
      me.performSelection(
        me.prepareSelection(
          isSelected ? _focusedCell : null,
          isSelected ? null : _focusedCell,
          !selectionMode.multiSelect,
          isRowNumber
        )
      );
      keyEvent.preventDefault();
    }
    /**
     * Selects a range of cells, from a cell selector (`GridLocation`) to another
     * @param {Grid.util.GridLocation|GridLocationConfig} from
     * @param {Grid.util.GridLocation|GridLocationConfig} to
     * @category Selection
     */
    selectCellRange(from, to) {
      this.performSelection(this.internalSelectRange(from, to));
    }
    // endregion
    // region Private convenience functions & properties
    getSelection() {
      if (this.isRowSelectionMode) {
        return this.selectedRecords;
      } else {
        return this.selectedCells;
      }
    }
    // Makes sure the same record or cell isn't deselected and selected at the same time. Selection will take precedence
    cleanSelectionChange(selectionChange) {
      var _a6, _b2;
      const {
        deselectedRecords,
        deselectedCells,
        selectedCells,
        deselectedCellRecords
      } = selectionChange, selectedRecordIds = (_a6 = selectionChange.selectedRecords) == null ? void 0 : _a6.map((r) => r.id), selectedCellRecordIds = (_b2 = selectionChange.selectedCellRecords) == null ? void 0 : _b2.map((r) => r.id);
      if ((deselectedRecords == null ? void 0 : deselectedRecords.length) && (selectedRecordIds == null ? void 0 : selectedRecordIds.length)) {
        selectionChange.deselectedRecords = deselectedRecords.filter((dr) => !selectedRecordIds.includes(dr.id));
      }
      if ((deselectedCells == null ? void 0 : deselectedCells.length) && (selectedCells == null ? void 0 : selectedCells.length)) {
        selectionChange.deselectedCells = deselectedCells.filter((dc) => !selectedCells.some((sc) => dc.equals(sc, true)));
      }
      if (deselectedCellRecords.length && (selectedCellRecordIds.length || (selectedRecordIds == null ? void 0 : selectedRecordIds.length))) {
        selectionChange.deselectedCellRecords = deselectedCellRecords.filter((dcr) => {
          return !selectedCellRecordIds.includes(dcr.id) && !selectedRecordIds.includes(dcr.id);
        });
      }
      return selectionChange;
    }
    getSelectedCellsForRecords(records) {
      return this._selectedCells.filter((cell) => cell.id && records.some((record) => record.id === cell.id));
    }
    delayUntilMouseUp(fn2) {
      const detacher2 = EventHelper.on({
        element: this.selectionDragMouseEventListenerElement,
        blur: (ev) => fn2(ev, detacher2),
        mouseup: (ev) => fn2(ev, detacher2),
        thisObj: this,
        once: true
      });
    }
    get isRowSelectionMode() {
      return !this.isCellSelectionMode;
    }
    get isCellSelectionMode() {
      return this.selectionMode.cell === true;
    }
    // Checks if rowNumber is activated and that all arguments (cellselectors) is of type rownumber
    isRowNumberSelecting(...selectors) {
      return this.selectionMode.rowNumber && !selectors.some(({ column: { type } }) => type !== "rownumber" && type !== "sequence");
    }
    selectionShouldIncludeChildren(record) {
      var _a6;
      const { includeChildren, multiSelect } = this.selectionMode;
      return (includeChildren === "always" || includeChildren === true && this._isCheckboxSelecting) && multiSelect && !record.isLeaf && ((_a6 = record.allChildren) == null ? void 0 : _a6.length);
    }
    // endregion
    //region Navigation
    // Used by keyMap to extend selection range
    extendSelectionLeft() {
      this.extendSelection("Left");
    }
    // Used by keyMap to extend selection range
    extendSelectionRight() {
      this.extendSelection("Right");
    }
    // Used by keyMap to extend selection range
    extendSelectionUp() {
      this.extendSelection("Up");
    }
    // Used by keyMap to extend selection range
    extendSelectionDown() {
      this.extendSelection("Down");
    }
    // Used by keyMap to extend selection range
    extendSelection(dir) {
      this._isKeyboardRangeSelecting = true;
      this["navigate" + dir]();
      this._isKeyboardRangeSelecting = false;
    }
    // Called from GridNavigation on mouse or keyboard events
    // Single entry point for all default user selection actions
    onCellNavigate(me, fromCellSelector, toCellSelector, doSelect) {
      var _a6, _b2;
      const {
        selectionMode,
        _selectionListenersDetachers
      } = me, {
        multiSelect,
        alwaysMultiSelect,
        deselectOnClick,
        dragSelect
      } = selectionMode, { ctrlKeyDown, shiftKeyDown } = GlobalEvents_default, isMouseLeft = GlobalEvents_default.isMouseDown(), isMouseRight = GlobalEvents_default.isMouseDown(2), currentEvent = GlobalEvents_default.currentMouseDown || GlobalEvents_default.currentKeyDown;
      toCellSelector = me.normalizeCellContext(toCellSelector);
      if (me.selectionDisabled || !doSelect || // Do not affect selection if navigating into header row.
      toCellSelector.rowIndex === -1 || ((_a6 = toCellSelector.record) == null ? void 0 : _a6.isGroupHeader) || // Don't allow keyboard selection if keyboardNavigation is deactivated
      (currentEvent == null ? void 0 : currentEvent.fromKeyMap) && !selectionMode.selectOnKeyboardNavigation || // CheckColumn events are handled by the CheckColumn itself.
      me.columns.getById(toCellSelector.columnId) === me.checkboxSelectionColumn || selectionMode.checkboxOnly || // Don't select if event was handled elsewhere
      (currentEvent == null ? void 0 : currentEvent.handled) === true && !me._fromFocusCell) {
        return;
      }
      if (!shiftKeyDown) {
        me._isAddingToSelection = (ctrlKeyDown || alwaysMultiSelect) && multiSelect;
        me._selectionStartCell = toCellSelector;
      }
      if (multiSelect && dragSelect && isMouseLeft && !_selectionListenersDetachers.selectiondrag && !me.preventDragSelect) {
        _selectionListenersDetachers.selectiondrag = EventHelper.on({
          name: "selectiondrag",
          element: me.selectionDragMouseEventListenerElement,
          blur: "onSelectionEnd",
          mouseup: {
            handler: "onSelectionEnd",
            element: globalThis
          },
          mousemove: "onSelectionDrag",
          thisObj: me
        });
      }
      me.preventDragSelect = false;
      const startCell = me._selectionStartCell, adding = me._isAddingToSelection;
      if ((shiftKeyDown && isMouseLeft || me._isKeyboardRangeSelecting) && startCell && multiSelect) {
        me.performSelection(
          me.internalSelectRange(
            startCell,
            toCellSelector,
            me.isRowNumberSelecting(startCell, toCellSelector)
          )
        );
      } else {
        let delay2 = false, continueSelecting = true, deselect;
        if (me.isCellSelected(toCellSelector, true)) {
          if (isMouseRight) {
            return;
          }
          if (adding || deselectOnClick) {
            deselect = [toCellSelector];
            continueSelecting = false;
          } else if (me.selectedRecords.length + (me.isCellSelectionMode ? me._selectedCells.length : 0) <= 1) {
            return;
          }
          delay2 = deselectOnClick || multiSelect;
        }
        if (!deselect && !adding) {
          deselect = null;
          if (dragSelect && delay2 && _selectionListenersDetachers.selectiondrag) {
            me._clearSelectionOnSelectionDrag = true;
          }
        }
        const finishSelection = (mouseUpEvent, detacher2) => {
          var _a7;
          detacher2 == null ? void 0 : detacher2();
          if (DragHelper.dragging) {
            return;
          }
          if (((_a7 = mouseUpEvent == null ? void 0 : mouseUpEvent.target) == null ? void 0 : _a7.nodeType) === Node.ELEMENT_NODE) {
            const mouseUpSelector = new GridLocation(mouseUpEvent.target);
            if ((mouseUpSelector == null ? void 0 : mouseUpSelector.grid) && !mouseUpSelector.equals(toCellSelector, true)) {
              return;
            }
          }
          if (!shiftKeyDown) {
            me._shiftSelectRange = null;
          }
          me.performSelection(
            me.prepareSelection(
              deselect,
              continueSelecting && [toCellSelector],
              deselect === null,
              continueSelecting && me.isRowNumberSelecting(toCellSelector)
            )
          );
        };
        if ((_b2 = me.features.rowReorder) == null ? void 0 : _b2.isDragging) {
          return;
        }
        if (delay2) {
          me.delayUntilMouseUp(finishSelection);
        } else {
          finishSelection();
        }
      }
    }
    // endregion
    // region Internal selection & deselection functions
    /**
     * Used internally to prepare a number of cells or records for selection/deselection depending on if cell
     * selectionMode is activated. This function will not select/deselect anything by itself
     * (that's done in performSelection).
     * @param {GridLocationConfig[]|Core.data.Model[]} cellSelectorsToDeselect Array of cell selectors or records.
     * @param {GridLocationConfig[]|Core.data.Model[]} cellSelectorsToSelect Array of cell selectors or records.
     * @param {Boolean} deselectAll Set to `true` to clear all selected records and cells.
     * @param {Boolean} forceRecordSelection Set to `true` to force record selection even if cell selection is active.
     * @returns {Object} selectionChange object to use for UI update
     * @private
     * @category Selection
     */
    prepareSelection(cellSelectorsToDeselect, cellSelectorsToSelect, deselectAll = false, forceRecordSelection = false) {
      const me = this, {
        _isSelectionDragging: isDragging,
        store
      } = me, {
        includeParents,
        selectRecordOnCell
      } = me.selectionMode, selectedCells = [], deselectedCells = deselectAll ? me._selectedCells : [], selectedRecords = [], deselectedRecords = deselectAll ? me._selectedRows : [], deselectedRecordIndex = ArrayHelper.keyBy(deselectedRecords, "id", () => 1), deselectedCellRecords = deselectAll ? me.selectedRecords.filter((r) => !deselectedRecordIndex[r.id]) : [];
      let selectedCellRecords = [];
      if (!deselectAll && cellSelectorsToDeselect) {
        for (const selector of ArrayHelper.asArray(cellSelectorsToDeselect)) {
          const cellSelector = me.normalizeCellContext(selector), record = (cellSelector == null ? void 0 : cellSelector.record) || (selector.isModel ? selector : store.getById(cellSelector.id));
          if (cellSelector.isSpecialRow) {
            continue;
          }
          deselectedCells.push(cellSelector);
          if (record && !deselectedRecordIndex[record.id]) {
            if (isDragging || me.isSelected(record)) {
              deselectedRecords.push(record);
              deselectedRecordIndex[record.id] = 1;
            } else if (selectRecordOnCell && me.selectedRecordCollection.get(record.id) && !deselectedCellRecords.some((dr) => dr.id === record.id)) {
              deselectedCellRecords.push(record);
            }
            if (me.selectionShouldIncludeChildren(record)) {
              for (const child of record.allChildren) {
                if (!deselectedRecordIndex[child.id] && (isDragging || me.isSelected(child))) {
                  deselectedRecords.push(child);
                  deselectedRecordIndex[child.id] = 1;
                }
              }
            }
          }
        }
      }
      if (cellSelectorsToSelect) {
        const selectedRecordIndex = {};
        for (const selector of ArrayHelper.asArray(cellSelectorsToSelect)) {
          const cellSelector = me.normalizeCellContext(selector), record = (cellSelector == null ? void 0 : cellSelector.record) || (selector.isModel ? selector : store.getById(cellSelector.id));
          if (cellSelector.isSpecialRow) {
            continue;
          }
          if (me.isCellSelectionMode && !forceRecordSelection) {
            selectedCells.push(cellSelector);
          }
          if ((me.isRowSelectionMode || forceRecordSelection) && !selectedRecordIndex[record.id]) {
            selectedRecords.push(record);
            selectedRecordIndex[record.id] = 1;
            if (me.selectionShouldIncludeChildren(record)) {
              for (const child of record.allChildren) {
                if (!selectedRecordIndex[child.id]) {
                  selectedRecords.push(child);
                  selectedRecordIndex[child.id] = 1;
                }
              }
            }
          }
        }
        if (selectRecordOnCell && selectedCells.length) {
          selectedCellRecords = ArrayHelper.unique(selectedCells.map((c) => c.record)).filter((r) => !selectedRecordIndex[r.id]);
        }
      }
      if (includeParents && (deselectedRecords.length || selectedRecords.length)) {
        const allChanges = [...deselectedRecords, ...selectedRecords], lowestLevelParents = ArrayHelper.unique(
          allChanges.filter((rec) => rec.parent && !rec.allChildren.some((child) => allChanges.includes(child))).map((rec) => rec.parent)
        );
        lowestLevelParents.forEach((parent) => me.toggleParentSelection(parent, selectedRecords, deselectedRecords));
      }
      return me.cleanSelectionChange({
        selectedCells,
        selectedRecords,
        deselectedCells,
        deselectedRecords,
        deselectAll,
        action: (selectedRecords == null ? void 0 : selectedRecords.length) || (selectedCells == null ? void 0 : selectedCells.length) ? "select" : "deselect",
        selectedCellRecords,
        deselectedCellRecords
      });
    }
    toggleParentSelection(parent, toSelect, toDeselect) {
      if (!parent || parent.isRoot) {
        return;
      }
      const isSelected = this.isSelected(parent), inToSelect = toSelect.includes(parent), inToDeselect = toDeselect.includes(parent), childIsSelected = (child) => this.isSelected(child) && !toDeselect.includes(child) || toSelect.includes(child);
      if (this.selectionMode.includeParents === "some") {
        if (parent.allChildren.some(childIsSelected)) {
          if ((!isSelected || inToDeselect) && !inToSelect) {
            toSelect.push(parent);
          }
        } else if (isSelected && !inToDeselect) {
          toDeselect.push(parent);
        }
      } else {
        if (isSelected) {
          if (!inToDeselect && !inToSelect && parent.allChildren.some((child) => toDeselect.includes(child))) {
            toDeselect.push(parent);
          }
        } else if (!inToSelect) {
          if (parent.allChildren.every(childIsSelected)) {
            toSelect.push(parent);
          }
        }
      }
      if (parent.parent) {
        this.toggleParentSelection(parent.parent, toSelect, toDeselect);
      }
    }
    /**
     * Used internally to select a range of cells or records depending on selectionMode. Used in both shift-selection
     * and for drag selection. Will remember current selection range and replace it with new one when it changes. But a
     * range which is completed (drag select mouse up or a new shift range starting point has been set) will remain.
     * This function will not update UI (that's done in refreshGridSelectionUI).
     * @param {GridLocationConfig} fromSelector
     * @param {GridLocationConfig} toSelector
     * @returns {Object} selectionChange object to use for UI update
     * @private
     * @category Selection
     */
    internalSelectRange(fromSelector, toSelector, forceRecordSelection = false) {
      const me = this, selectRecords = me.isRowSelectionMode || forceRecordSelection, selectionChange = me.prepareSelection(
        me._shiftSelectRange,
        me.getRange(fromSelector, toSelector, selectRecords),
        false,
        forceRecordSelection
      );
      me._shiftSelectRange = selectionChange[`selected${selectRecords ? "Records" : "Cells"}`];
      return selectionChange;
    }
    /**
     * Used internally to get a range of cell selectors from a start selector to an end selector.
     * @private
     */
    getRange(fromSelector, toSelector, selectRecords = false) {
      const me = this, { store } = me, fromCell = me.normalizeCellContext(fromSelector), toCell = me.normalizeCellContext(toSelector), startRowIndex = Math.min(fromCell.rowIndex, toCell.rowIndex), endRowIndex = Math.max(fromCell.rowIndex, toCell.rowIndex), toSelect = [], startColIndex = Math.min(fromCell.columnIndex, toCell.columnIndex), endColIndex = Math.max(fromCell.columnIndex, toCell.columnIndex);
      if (startRowIndex === -1 || endRowIndex === -1) {
        throw new Error("Record not found in selectRange");
      }
      if (selectRecords) {
        const range = store.getRange(startRowIndex, endRowIndex + 1, false);
        if (toCell.rowIndex < fromCell.rowIndex) {
          range.reverse();
        }
        toSelect.push(...range);
      } else {
        for (let rIx = startRowIndex; rIx <= endRowIndex; rIx++) {
          for (let cIx = startColIndex; cIx <= endColIndex; cIx++) {
            toSelect.push({ rowIndex: rIx, columnIndex: cIx });
          }
        }
      }
      return toSelect.map((s) => me.normalizeCellContext(s));
    }
    // endregion
    // region Update UI & trigger events
    performSelection(selectionChange, updateUI = true, silent = false) {
      const me = this, {
        selectedRecords = [],
        selectedCells = [],
        selectedCellRecords = [],
        deselectedRecords = [],
        deselectedCells = [],
        deselectedCellRecords = [],
        action
      } = selectionChange, allSelectedRecords = [...selectedRecords, ...selectedCellRecords], allDeselectedRecords = [...deselectedRecords, ...deselectedCellRecords], rowMode = me.isRowSelectionMode;
      if (me.trigger("beforeSelectionChange", {
        mode: rowMode ? "row" : "cell",
        action,
        selected: allSelectedRecords,
        deselected: allDeselectedRecords,
        selection: me.selectedRecords,
        selectedCells,
        deselectedCells,
        cellSelection: me.selectedCells
      }) === false) {
        me.removeDragListeners();
        return false;
      }
      if (me._selectedCells === deselectedCells) {
        me._selectedCells = [];
      } else {
        const keepCells = [];
        for (const selectedCell of me._selectedCells) {
          if (
            // Keep selected cells that are not to be deselected and to preserve selection order, not to be
            // selected again
            !deselectedCells.some((cellSelector) => selectedCell.equals(cellSelector, true)) && !selectedCells.some((cellSelector) => selectedCell.equals(cellSelector, true))
          ) {
            keepCells.push(selectedCell);
          }
        }
        me._selectedCells = keepCells;
      }
      selectionChange.deselectedRecords = [...deselectedRecords];
      if (deselectedRecords === me._selectedRows) {
        me._selectedRows.length = 0;
        me.changeSelectedRecordCollectionSilent((c) => c.clear());
      } else {
        const keepRecords = [], keepInCollection = [], allDeselectedRecordsIndex = ArrayHelper.keyBy(allDeselectedRecords, "id", () => 1);
        for (const selectedRecord of me.selectedRecords) {
          if (!allDeselectedRecordsIndex[selectedRecord.id]) {
            if (me.isSelected(selectedRecord)) {
              keepRecords.push(selectedRecord);
            } else {
              keepInCollection.push(selectedRecord);
            }
          }
        }
        me._selectedRows = keepRecords;
        me.changeSelectedRecordCollectionSilent((c) => c.values = [...keepRecords, ...keepInCollection]);
      }
      if (selectedCells.length) {
        for (const selectedCell of selectedCells) {
          if (!me._selectedCells.some((cellSelector) => cellSelector.equals(selectedCell, true))) {
            me._selectedCells.push(selectedCell);
          }
        }
      }
      if (selectedRecords.length) {
        me._selectedRows.push(...selectedRecords.filter((r) => !me._selectedRows.some((sr) => sr.id === r.id)));
        me.changeSelectedRecordCollectionSilent((c) => c.add(...selectedRecords));
      }
      if (selectedCellRecords.length) {
        me.changeSelectedRecordCollectionSilent((c) => c.add(...selectedCellRecords));
      }
      if (updateUI) {
        me.refreshGridSelectionUI(selectionChange);
      }
      me.afterSelectionChange(selectionChange);
      if (!silent) {
        me.triggerSelectionChangeEvent(selectionChange);
      }
    }
    // Makes sure the DOM is up-to-date with current selection.
    refreshGridSelectionUI({ selectedRecords, selectedCells, deselectedRecords, deselectedCells }) {
      const me = this, { checkboxSelectionColumn } = me;
      checkboxSelectionColumn == null ? void 0 : checkboxSelectionColumn.suspendEvents();
      me.updateGridSelectionRecords(selectedRecords, true);
      me.updateGridSelectionRecords(deselectedRecords, false);
      me.updateCheckboxHeader();
      checkboxSelectionColumn == null ? void 0 : checkboxSelectionColumn.resumeEvents();
      if (me.isCellSelectionMode) {
        me.updateGridSelectionCells(selectedCells, true);
        if (me.selectionMode.column) {
          me.updateGridSelectionColumns(selectedCells);
        }
      }
      me.updateGridSelectionCells(deselectedCells, false);
    }
    // Loops through records and updates Grid rows
    updateGridSelectionRecords(records, selected) {
      const { checkboxSelectionColumn } = this;
      if (records == null ? void 0 : records.length) {
        for (let i = 0; i < records.length; i++) {
          const row = this.getRowFor(records[i]);
          if (row) {
            row.toggleCls("b-selected", selected);
            row.setAttribute("aria-selected", selected);
            if (checkboxSelectionColumn && !checkboxSelectionColumn.hidden && !records[i].isSpecialRow) {
              row.getCell(checkboxSelectionColumn.id).widget.checked = selected;
            }
          }
        }
      }
    }
    // Loops through cell selectors and updates Grid cell's
    updateGridSelectionCells(cells, selected) {
      if (cells == null ? void 0 : cells.length) {
        for (let i = 0; i < cells.length; i++) {
          const cell = this.getCell(cells[i]);
          if (cell) {
            cell.setAttribute("aria-selected", selected);
            cell.classList.toggle("b-selected", selected);
          }
        }
      }
    }
    // Loops through columns to toggle their selected state
    updateGridSelectionColumns(selectedCells) {
      var _a6;
      const { count } = this.store;
      for (const column of this.columns.visibleColumns) {
        (_a6 = column.element) == null ? void 0 : _a6.classList.toggle(
          "b-selected",
          (selectedCells == null ? void 0 : selectedCells.filter((s) => s.columnId === column.id).length) === count
        );
      }
    }
    triggerSelectionChangeEvent(selectionChange) {
      const {
        selectedRecords = [],
        selectedCells = [],
        selectedCellRecords = [],
        deselectedRecords = [],
        deselectedCells = [],
        deselectedCellRecords = []
      } = selectionChange, allSelectedRecords = [...selectedRecords, ...selectedCellRecords], allDeselectedRecords = [...deselectedRecords, ...deselectedCellRecords], rowMode = this.isRowSelectionMode;
      this.trigger("selectionChange", {
        mode: rowMode ? "row" : "cell",
        action: selectionChange.action,
        selected: allSelectedRecords,
        deselected: allDeselectedRecords,
        selection: this.selectedRecords,
        selectedCells,
        deselectedCells,
        cellSelection: this.selectedCells
      });
    }
    //endregion
    doDestroy() {
      var _a6;
      ((_a6 = this.selectedRecordCollection) == null ? void 0 : _a6.owner) === this && this.selectedRecordCollection.destroy();
      this._selectedCells.length = 0;
      this._selectedRows.length = 0;
      for (const detacher2 in this._selectionListenersDetachers) {
        this._selectionListenersDetachers[detacher2]();
      }
      super.doDestroy();
    }
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "GridSelection"), __publicField(_a5, "configurable", {
    /**
     * Selection configuration settings, change these properties to control how selection works and what can be
     * selected.
     *
     * When *configuring* (creating an instance), set this to an object containing only those properties you want to
     * *change*.
     *
     * ```javascript
     * new Grid({
     *     selectionMode : {
     *        // Enables cell selection
     *        cell       : true,
     *        // Enabled cell and record selection by dragging
     *        dragSelect : true,
     *        // Enables selection of a column's cells by clicking the column header
     *        column     : true,
     *        // Shows a row number column which, when clicked, selected the row/record
     *        rowNumber  : true
     *     }
     * });
     * ```
     *
     * After the configuring is done, these properties will be monitored for changes, making it possible to alter
     * the selection configuration at any time.
     *
     * ```javascript
     * // Adds a selection checkbox column and makes that the only way to select
     * grid.selectionMode.checkboxOnly = true;
     *
     * // Deactivates the auto selection of rows and cells when keyboard navigating
     * grid.selectionMode.selectOnKeyboardNavigation = false;
     * ```
     *
     * @prp {GridSelectionMode} selectionMode
     * @includetypedef
     * @default
     * @category Selection
     */
    selectionMode: {
      cell: false,
      multiSelect: true,
      checkboxOnly: false,
      checkbox: false,
      checkboxPosition: null,
      showCheckAll: false,
      deselectFilteredOutRecords: false,
      includeChildren: false,
      includeParents: false,
      preserveSelectionOnPageChange: false,
      preserveSelectionOnDatasetChange: true,
      deselectOnClick: false,
      dragSelect: false,
      selectOnKeyboardNavigation: true,
      column: false,
      rowNumber: false,
      selectRecordOnCell: true
    },
    keyMap: {
      "Shift+ArrowUp": "extendSelectionUp",
      "Shift+ArrowDown": "extendSelectionDown",
      "Shift+ArrowLeft": "extendSelectionLeft",
      "Shift+ArrowRight": "extendSelectionRight",
      " ": { handler: "toggleSelection", weight: 10 }
    },
    selectedRecordCollection: {},
    selectionDisabled: false
  }), _a5;
};

// ../Grid/lib/Grid/view/mixin/GridState.js
var suspendStoreEvents = (subGrid) => subGrid.columns.suspendEvents();
var resumeStoreEvents = (subGrid) => subGrid.columns.resumeEvents();
var fillSubGridColumns = (subGrid) => {
  subGrid.columns.clearCaches();
  subGrid.columns.fillFromMaster();
};
var compareStateSortIndex = (a, b) => a.stateSortIndex - b.stateSortIndex;
var GridState_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    /**
     * Gets or sets grid's state. Check out {@link Grid.view.mixin.GridState} mixin for details.
     * @member {GridStateInfo} state
     * @category State
     */
    updateStore(store, was) {
      var _a6;
      (_a6 = super.updateStore) == null ? void 0 : _a6.call(this, store, was);
      this.detachListeners("stateStoreListeners");
      store == null ? void 0 : store.ion({
        name: "stateStoreListeners",
        filter: "triggerUpdate",
        group: "triggerUpdate",
        sort: "triggerUpdate",
        thisObj: this
      });
    }
    updateColumns(columns, was) {
      var _a6;
      (_a6 = super.updateColumns) == null ? void 0 : _a6.call(this, columns, was);
      this.detachListeners("stateColumnListeners");
      columns.ion({
        name: "stateColumnListeners",
        change: "triggerUpdate",
        sort: "triggerUpdate",
        thisObj: this
      });
    }
    updateRowManager(manager, was) {
      var _a6;
      (_a6 = super.updateRowManager) == null ? void 0 : _a6.call(this, manager, was);
      this.detachListeners("stateRowManagerListeners");
      manager.ion({
        name: "stateRowManagerListeners",
        rowHeight: "triggerUpdate",
        thisObj: this
      });
    }
    triggerUpdate() {
      this.trigger("stateChange");
    }
    finalizeInit() {
      super.finalizeInit();
      this.ion({
        selectionChange: "triggerUpdate",
        toggleGroup: ({ allRecords }) => !allRecords && this.triggerUpdate(),
        thisObj: this
      });
    }
    /**
     * Get grid's current state for serialization. State includes rowHeight, headerHeight, selectedCell,
     * selectedRecordId, column states and store state etc.
     * @returns {Object} State object to be serialized
     * @private
     */
    getState() {
      var _a6;
      const me = this, style = me.element.style.cssText, state = {
        rowHeight: me.rowHeight
      };
      if (style) {
        state.style = style;
      }
      if (me.selectedCell) {
        const { id: id2, columnId } = me.selectedCell;
        state.selectedCell = { id: id2, columnId };
      }
      if (!me.disableGridColumnIdWarning && me.columns.find((c) => c.meta.generatedId === c.id)) {
        console.warn("Column(s) without id detected. When saving and restoring state, columns without a configured id can be restored incorrectly.");
      }
      state.selectedRecords = me.selectedRecords.map((entry) => entry.id);
      state.columns = me.columns.allRecords.map((column) => column.getState());
      state.store = me.store.state;
      state.scroll = me.storeScroll();
      const treeGroupLevels = (_a6 = me.features.treeGroup) == null ? void 0 : _a6.levels;
      if ((treeGroupLevels == null ? void 0 : treeGroupLevels.length) > 0 && !treeGroupLevels.some((groupFn) => !groupFn.fieldName)) {
        state.treeGroupLevels = me.features.treeGroup.levels.map((fn2) => fn2.fieldName);
      }
      state.subGrids = {};
      me.eachSubGrid((subGrid) => {
        var _a7;
        const config = state.subGrids[subGrid.region] = state.subGrids[subGrid.region] || {};
        if (subGrid.isPainted) {
          if (subGrid.flex == null) {
            config.width = subGrid.width;
          }
        } else {
          if (subGrid.config.width != null) {
            config.width = subGrid.config.width;
          } else {
            config.flex = subGrid.config.flex;
          }
        }
        config.collapsed = (_a7 = subGrid.collapsed) != null ? _a7 : false;
        if (config.collapsed) {
          config._beforeCollapseState = subGrid._beforeCollapseState;
        }
      });
      for (const [key, feature] of Object.entries(this.features)) {
        const featureState = feature.state;
        if (featureState) {
          state.features = state.features || {};
          state.features[key] = featureState;
        }
      }
      return state;
    }
    /**
     * Apply previously stored state.
     * @param {Object} state
     * @private
     */
    applyState(state) {
      var _a6, _b2;
      const me = this, { columns } = me;
      state = Object.assign({}, state);
      me.suspendRefresh();
      if ("columns" in state) {
        let columnsChanged = false, needSort = false;
        columns.suspendEvents();
        me.eachSubGrid(suspendStoreEvents);
        const stateApplied = /* @__PURE__ */ new Set(), stateColumn = /* @__PURE__ */ new Map();
        state.columns.forEach((columnState, index) => {
          var _a7, _b3;
          const column = (_b3 = columns.lookupStateColumns(columnState).filter((c) => !stateApplied.has(c))[0]) != null ? _b3 : ((_a7 = me.stateSettings) == null ? void 0 : _a7.restoreUnconfiguredColumns) && // try to recreate the column
          columns.add({ id: columnState.id, type: columnState.type, field: columnState.field })[0];
          if (column) {
            stateApplied.add(column);
            stateColumn.set(columnState, column);
            const columnGeneration = column.generation;
            if ("region" in columnState && !(columnState.region in me.subGrids)) {
              delete columnState.region;
              delete columnState.locked;
            }
            column.applyState(columnState);
            columnsChanged = columnsChanged || column.generation !== columnGeneration;
            column.stateSortIndex = index;
            if (column.allIndex !== index) {
              needSort = columnsChanged = true;
            }
          }
        });
        state.columns.forEach((columnState) => {
          if ("parentId" in columnState) {
            const parentStateConfig = state.columns.find((cfg) => cfg.id === columnState.parentId), parentColumn = stateColumn.get(parentStateConfig), childCol = stateColumn.get(columnState);
            if (parentColumn && childCol && childCol.parent !== parentColumn) {
              parentColumn.insertChild(childCol);
            }
          }
        });
        if (columnsChanged) {
          me.eachSubGrid(fillSubGridColumns);
        }
        if (needSort) {
          me.eachSubGrid((subGrid) => {
            subGrid.columns.records.sort(compareStateSortIndex);
            subGrid.columns.allRecords.sort(compareStateSortIndex);
          });
          columns.sort({
            fn: compareStateSortIndex,
            // always sort ascending
            ascending: true
          });
        }
        if (me.isPainted && columnsChanged) {
          me.renderContents();
        }
        columns.resumeEvents();
        me.eachSubGrid(resumeStoreEvents);
        delete state.columns;
      }
      if ("subGrids" in state) {
        me.eachSubGrid((subGrid) => {
          if (subGrid.region in state.subGrids) {
            const subGridState = state.subGrids[subGrid.region];
            if ("width" in subGridState) {
              subGrid.width = subGridState.width;
            } else if ("flex" in subGridState) {
              subGrid.flex = subGridState.flex;
            }
            if ("collapsed" in subGridState) {
              subGrid.collapsed = subGridState.collapsed;
              subGrid._beforeCollapseState = subGridState._beforeCollapseState;
            }
          }
          subGrid.clearWidthCache();
        });
        delete state.subGrids;
      }
      if ("store" in state) {
        me.store.state = state.store;
        delete state.store;
      }
      const scrollState = state.scroll;
      delete state.scroll;
      if ("features" in state) {
        for (const key in state.features) {
          (_b2 = (_a6 = this.features[key]) == null ? void 0 : _a6.setConfig) == null ? void 0 : _b2.call(_a6, state.features[key]);
        }
        delete state.features;
      }
      ObjectHelper.assign(me, state);
      if ("treeGroupLevels" in state && me.features.treeGroup) {
        me.features.treeGroup.levels = state.treeGroupLevels;
      }
      me.resumeRefresh(true);
      me.whenVisible(() => me.applyScrollState(scrollState));
    }
    applyScrollState(scrollState) {
      const me = this;
      me.eachSubGrid((s) => s.refreshFakeScroll());
      if (scrollState) {
        me.restoreScroll(scrollState);
        if (scrollState.scrollTop) {
          me.element.querySelectorAll(".b-resize-monitored").forEach((element) => {
            const widget = Widget.fromElement(element);
            if (widget) {
              widget.onElementResize(element);
            }
          });
        }
      }
    }
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "GridState"), // region events
  /**
   * Fired before state is saved by the StateProvider. Allows editing the state object or preventing the operation.
   * @event beforeStateSave
   * @preventable
   * @param {GridStateInfo} state State object config
   */
  /**
   * Fired before state is applied to the source. Allows editing the state object or preventing the operation.
   * @event beforeStateApply
   * @preventable
   * @param {GridStateInfo} state State object config
   */
  // endregion
  __publicField(_a5, "configurable", {
    statefulEvents: [
      "subGridCollapse",
      "subGridExpand",
      "horizontalScrollEnd",
      "verticalScrollEnd",
      "stateChange",
      "selectionChange",
      "collapseAllGroups",
      "expandAllGroups",
      "treeGroupChange"
    ],
    /**
    * The properties of this settings object controls how grid is restored from state data.
     *
     * To disable state restoration for unconfigured columns:
     *
     * ```javascript
     * const grid = new Grid({
     *     stateSettings : {
     *         restoreUnconfiguredColumns : false
     *     }
     * });
     * ```
     *
     * @prp {Object}
     * @property {Boolean} [restoreUnconfiguredColumns=true] Determines whether to restore columns from the state that are not explicitly defined in the grid configuration.
     */
    stateSettings: {
      restoreUnconfiguredColumns: true
    }
  }), _a5;
};

// ../Grid/lib/Grid/util/SubGridScroller.js
var immediatePromise7 = Promise.resolve();
var defaultScrollOptions2 = {
  block: "nearest"
};
var SubGridScroller = class extends Scroller {
  // The Grid's main Y scroller keeps a list of the X scrollers
  updateYScroller(yScroller) {
    yScroller == null ? void 0 : yScroller.addScroller(this);
  }
  scrollIntoView(element, options = defaultScrollOptions2) {
    const me = this, { xDelta, yDelta } = me.getDeltaTo(element, options);
    let result = xDelta || yDelta ? me.scrollBy(xDelta, yDelta, options) : immediatePromise7;
    if (options.highlight || options.focus) {
      const scrollPromise2 = result;
      result = result.then(() => {
        var _a5, _b2;
        if (scrollPromise2.cancelled) {
          return;
        }
        element = element instanceof Rectangle ? element : (_b2 = (_a5 = options.elementAfterScroll) == null ? void 0 : _a5.call(options)) != null ? _b2 : element;
        if (options.highlight) {
          if (element instanceof Rectangle) {
            element.translate(-xDelta, -yDelta).highlight();
          } else if (typeof options.highlight === "boolean") {
            DomHelper.highlight(element);
          } else {
            me.callback(options.highlight, me, [element]);
          }
        }
        if (options.focus) {
          element.focus({ preventScroll: true });
        }
      });
      result.cancel = () => {
        var _a5;
        return (_a5 = scrollPromise2.cancel) == null ? void 0 : _a5.call(scrollPromise2);
      };
    }
    return result;
  }
  scrollBy(xDelta, yDelta, options) {
    const yPromise = yDelta && this.yScroller.scrollBy(0, yDelta, options), xPromise = xDelta && super.scrollBy(xDelta, 0, options), result = Promise.all([xPromise, yPromise]);
    result.cancel = () => {
      var _a5, _b2;
      (_a5 = xPromise == null ? void 0 : xPromise.cancel) == null ? void 0 : _a5.call(xPromise);
      (_b2 = yPromise == null ? void 0 : yPromise.cancel) == null ? void 0 : _b2.call(yPromise);
      result.cancelled = true;
    };
    if ((xPromise == null ? void 0 : xPromise.cancel) && (yPromise == null ? void 0 : yPromise.cancel)) {
      options.force = true;
    }
    return result;
  }
  scrollTo(toX, toY, options) {
    const yPromise = toY != null && this.yScroller.scrollTo(null, toY, options), xPromise = toX != null && super.scrollTo(toX, null, options);
    if (!(options && options.animate)) {
      this.syncPartners();
    }
    const result = Promise.all([xPromise, yPromise]);
    result.cancel = () => {
      var _a5, _b2;
      (_a5 = xPromise == null ? void 0 : xPromise.cancel) == null ? void 0 : _a5.call(xPromise);
      (_b2 = yPromise == null ? void 0 : yPromise.cancel) == null ? void 0 : _b2.call(yPromise);
      result.cancelled = true;
    };
    if ((xPromise == null ? void 0 : xPromise.cancel) && (yPromise == null ? void 0 : yPromise.cancel)) {
      options.force = true;
    }
    return result;
  }
  get viewport() {
    const elementBounds = Rectangle.from(this.element), viewport = this.yScroller ? elementBounds.intersect(Rectangle.from(this.yScroller.element)) : elementBounds;
    return viewport || new Rectangle(elementBounds.x, elementBounds.y, elementBounds.width, 0);
  }
  set y(y) {
    if (this.yScroller) {
      this.yScroller.y = y;
    }
  }
  get y() {
    return this.yScroller ? this.yScroller.y : 0;
  }
  get maxY() {
    return this.yScroller ? this.yScroller.maxY : 0;
  }
  get scrollHeight() {
    return this.yScroller ? this.yScroller.scrollHeight : 0;
  }
  get clientHeight() {
    return this.yScroller ? this.yScroller.clientHeight : 0;
  }
  suspendEvents() {
    var _a5;
    super.suspendEvents();
    (_a5 = this.yScroller) == null ? void 0 : _a5.suspendEvents();
  }
  resumeEvents() {
    var _a5;
    super.resumeEvents();
    (_a5 = this.yScroller) == null ? void 0 : _a5.resumeEvents();
  }
};
// SubGrids do not drive the scrollWidth of their partners (Header and Footer)
// SubGrids scrollWidth is propagated from the Header by SubGrid.refreshFakeScroll.
__publicField(SubGridScroller, "configurable", {
  propagate: false,
  overflowX: "hidden-scroll",
  yScroller: null
});
SubGridScroller._$name = "SubGridScroller";

// ../Grid/lib/Grid/view/SubGrid.js
var sumWidths = (t, e) => t + e.getBoundingClientRect().width;
var SubGrid = class extends Widget {
  //endregion
  //region Init
  /**
   * SubGrid constructor
   * @param config
   * @private
   */
  construct(config) {
    super.construct(config);
    this.rowManager.ion({ addRows: "onAddRow", thisObj: this });
    if (VersionHelper.isTestEnv) {
      this.hideOverlayScroller.delay = 50;
    }
  }
  doDestroy() {
    var _a5;
    const me = this;
    me.header.destroy();
    me.footer.destroy();
    (_a5 = me.fakeScroller) == null ? void 0 : _a5.destroy();
    me.virtualScrollerElement.remove();
    me.splitterElements.forEach((element) => element.remove());
    super.doDestroy();
  }
  get barConfig() {
    const me = this, { width, flex } = me.element.style, config = {
      subGrid: me,
      parent: me,
      // Contained widgets need to know their parents
      maxWidth: me.maxWidth || void 0,
      minWidth: me.minWidth || void 0
    };
    if (flex) {
      config.flex = flex;
    } else if (width) {
      config.width = width;
    }
    return config;
  }
  changeHeader(header) {
    return new this.headerClass(ObjectHelper.assign({
      id: this.id + "-header"
    }, this.barConfig, header));
  }
  changeFooter(footer) {
    return new this.footerClass(ObjectHelper.assign({
      id: this.id + "-footer"
    }, this.barConfig, footer));
  }
  //endregion
  //region Splitters
  get splitterElements() {
    return [this.splitterElement, this.headerSplitter, this.scrollerSplitter, this.footerSplitter];
  }
  /**
   * Toggle (add/remove) class for splitters
   * @param {String} cls class name
   * @param {Boolean} [add] Set to `true` to add class, `false` to remove
   * @param {Boolean} [includePartners] Set to `true` to also process partners
   * @private
   */
  toggleSplitterCls(cls, add = true, includePartners = true) {
    this.splitterElements.forEach((el) => el == null ? void 0 : el.classList[add ? "add" : "remove"](cls));
    if (includePartners) {
      this.scrollable.eachPartner(({ scroller }) => {
        var _a5, _b2;
        if (scroller.owner !== this) {
          (_b2 = (_a5 = scroller.owner).toggleSplitterCls) == null ? void 0 : _b2.call(_a5, cls === "b-hover" ? "b-partner-splitter-hover" : "b-hover", add, false);
        }
      });
    }
  }
  hideSplitter() {
    this.splitterElements.forEach((el) => el.classList.add("b-hide-display"));
    this.$showingSplitter = false;
  }
  showSplitter() {
    this.splitterElements.forEach((el) => el.classList.remove("b-hide-display"));
    this.$showingSplitter = true;
  }
  //endregion
  //region Template
  changeElement(element, was) {
    const { region } = this;
    return super.changeElement({
      tabIndex: -1,
      "aria-label": `${region} subgrid`,
      className: {
        "b-grid-subgrid": 1,
        [`b-grid-subgrid-${region}`]: region,
        "b-grid-subgrid-collapsed": this.collapsed
      },
      dataset: {
        region
      }
    }, was);
  }
  get rowElementConfig() {
    const { grid } = this;
    return {
      role: "row",
      className: grid.rowCls,
      children: this.columns.visibleColumns.map((column, columnIndex) => ({
        role: "gridcell",
        "aria-colindex": columnIndex + 1,
        tabIndex: grid.cellTabIndex,
        className: "b-grid-cell",
        dataset: {
          column: column.field || "",
          columnId: column.id
        }
      }))
    };
  }
  // Added to DOM in Grid `get bodyConfig`
  changeVirtualScrollerElement() {
    const references = DomHelper.createElement({
      role: "presentation",
      reference: "virtualScrollerElement",
      className: "b-virtual-scroller",
      tabIndex: -1,
      dataset: {
        region: this.region
      },
      children: [
        {
          reference: "virtualScrollerWidth",
          className: "b-virtual-width"
        }
      ]
    });
    this.virtualScrollerWidth = references.virtualScrollerWidth;
    return references.virtualScrollerElement;
  }
  changeSplitterElement() {
    const references = DomHelper.createElement({
      reference: "splitterElement",
      className: {
        "b-grid-splitter": 1,
        "b-grid-splitter-collapsed": this.collapsed,
        "b-hide-display": 1
        // GridSubGrids determines visibility
      },
      dataset: {
        region: this.region
      },
      children: [
        BrowserHelper.isTouchDevice ? { className: "b-splitter-touch-area" } : null,
        {
          className: "b-grid-splitter-inner b-grid-splitter-main",
          children: [
            {
              className: "b-grid-splitter-buttons",
              reference: "splitterButtons",
              children: [
                {
                  className: "b-grid-splitter-button-collapse",
                  children: [
                    BrowserHelper.isTouchDevice ? { className: "b-splitter-button-touch-area" } : null,
                    {
                      tag: "svg",
                      ns: "http://www.w3.org/2000/svg",
                      version: "1.1",
                      className: "b-grid-splitter-button-icon b-gridregion-collapse-arrow",
                      viewBox: "0 0 256 512",
                      children: [
                        {
                          tag: "path",
                          d: "M192 448c-8.188 0-16.38-3.125-22.62-9.375l-160-160c-12.5-12.5-12.5-32.75 0-45.25l160-160c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25L77.25 256l137.4 137.4c12.5 12.5 12.5 32.75 0 45.25C208.4 444.9 200.2 448 192 448z"
                        }
                      ]
                    }
                  ]
                },
                {
                  className: "b-grid-splitter-button-expand",
                  children: [
                    BrowserHelper.isTouchDevice ? { className: "b-splitter-button-touch-area" } : null,
                    {
                      tag: "svg",
                      ns: "http://www.w3.org/2000/svg",
                      version: "1.1",
                      className: "b-grid-splitter-button-icon b-gridregion-expand-arrow",
                      viewBox: "0 0 256 512",
                      children: [
                        {
                          tag: "path",
                          d: "M64 448c-8.188 0-16.38-3.125-22.62-9.375c-12.5-12.5-12.5-32.75 0-45.25L178.8 256L41.38 118.6c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0l160 160c12.5 12.5 12.5 32.75 0 45.25l-160 160C80.38 444.9 72.19 448 64 448z"
                        }
                      ]
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    });
    this.splitterButtons = references.splitterButtons;
    return references.splitterElement;
  }
  get splitterConfig() {
    return {
      className: this.splitterElement.className.trim(),
      children: [
        BrowserHelper.isTouchDevice ? { className: "b-splitter-touch-area" } : null,
        {
          className: "b-grid-splitter-inner"
        }
      ],
      dataset: {
        region: this.region
      }
    };
  }
  changeHeaderSplitter() {
    return DomHelper.createElement(this.splitterConfig);
  }
  changeScrollerSplitter() {
    return DomHelper.createElement(this.splitterConfig);
  }
  changeFooterSplitter() {
    return DomHelper.createElement(this.splitterConfig);
  }
  //endregion
  //region Render
  render(...args) {
    const me = this;
    super.render(...args);
    if (me.grid) {
      me.updateHasFlex();
      me.element.parentNode.insertBefore(me.splitterElement, me.element.nextElementSibling);
      me.splitterElements.forEach(
        (element) => EventHelper.on({
          element,
          mouseenter: "onSplitterMouseEnter",
          mouseleave: "onSplitterMouseLeave",
          thisObj: me
        })
      );
      me._collapsed && me.collapse();
    }
  }
  toggleHeaders(hide) {
    const me = this;
    if (hide) {
      me.headerSplitter.remove();
      me.header.element.remove();
      me.scrollable.removePartner(me.header.scrollable, "x");
    } else {
      const { grid } = me;
      if (!me.isConfiguring) {
        const index = grid.items.indexOf(me) * 2;
        DomHelper.insertAt(grid.headerContainer, me.headerSplitter, index);
        DomHelper.insertAt(grid.headerContainer, me.header.element, index);
        me.refreshHeader();
      }
      me.scrollable.addPartner(me.header.scrollable, "x");
    }
  }
  refreshHeader() {
    this.header.refreshContent();
  }
  refreshColumnHeader(column) {
    this.header.refreshColumn(column);
  }
  refreshFooter() {
    var _a5;
    (_a5 = this.footer) == null ? void 0 : _a5.refreshContent();
  }
  // Override to iterate header and footer.
  get childItems() {
    return [this.header, this.footer];
  }
  //endregion
  //region Size & resize
  /**
   * Sets cell widths. Cannot be done in template because of CSP
   * @private
   */
  fixCellWidths(rowElement) {
    const { visibleColumns } = this.columns;
    let cell = rowElement.firstElementChild, i = 0;
    while (cell) {
      const column = visibleColumns[i], { element } = column;
      if (column.minWidth) {
        cell.style.minWidth = DomHelper.setLength(column.minWidth);
      }
      if (column.maxWidth) {
        cell.style.maxWidth = DomHelper.setLength(column.maxWidth);
      }
      if (column.flex) {
        if (column.childLevel && element) {
          cell.style.flex = `0 0 ${element.getBoundingClientRect().width}px`;
          cell.style.width = "";
        } else {
          cell.style.flex = column.flex;
          cell.style.width = "";
        }
      } else if (column.width) {
        cell.style.width = DomHelper.setLength(column.width);
      } else {
        cell.style.flex = cell.style.width = cell.style.minWidth = "";
      }
      cell = cell.nextElementSibling;
      i++;
    }
  }
  get totalFixedWidth() {
    return this.columns.totalFixedWidth;
  }
  get headerScrollWidth() {
    return [...this.header.contentElement.children].reduce(sumWidths, 0) || this.columns.totalFixedWidth;
  }
  /**
   * Sets header width and scroller width (if needed, depending on if using flex). Might also change the subgrids
   * width, if it uses a width calculated from its columns.
   * @private
   */
  fixWidths() {
    const me = this, {
      element,
      header,
      footer
    } = me;
    if (!me.collapsed) {
      if (me.flex) {
        header.flex = me.flex;
        if (footer) {
          footer.flex = me.flex;
        }
        element.style.flex = me.flex;
      } else {
        if (me.hasCalculatedWidth && !me.columns.some((col) => !col.hidden && col.flex) && me.totalFixedWidth !== me.width) {
          me.width = me.totalFixedWidth;
          me.hasCalculatedWidth = true;
          return;
        }
        me.clearWidthCache();
        let totalWidth = me.width;
        if (!totalWidth && me.hasCalculatedWidth) {
          totalWidth = 0;
          for (const col of me.columns) {
            if (!col.flex && !col.hidden)
              totalWidth += col.width;
          }
        }
        element.style.width = `${totalWidth}px`;
        header.width = totalWidth;
        if (footer) {
          footer.width = totalWidth;
        }
      }
      me.handleHorizontalScroll(false);
    }
  }
  // Safari does not shrink cells the same way as chrome & ff does without having a width set on the row
  fixRowWidthsInSafariEdge() {
    if (BrowserHelper.isSafari) {
      const me = this, { region, header } = me, minWidth = header.calculateMinWidthForSafari();
      me.rowManager.forEach((row) => {
        const element = row.getElement(region);
        if (element) {
          element.style.width = `${minWidth}px`;
        }
      });
      header.headersElement.style.width = `${minWidth}px`;
    }
  }
  /**
   * Get/set SubGrid width, which also sets header and footer width (if available).
   * @property {Number}
   */
  set width(width) {
    var _a5, _b2;
    const me = this;
    me.hasCalculatedWidth = false;
    super.width = width;
    me.header.width = width;
    me.footer.width = width;
    if (me.isPainted) {
      me.onElementResize();
    }
    if (!me.isExpanding && !me.isCollapsing && !me.isConfiguring) {
      (_b2 = (_a5 = me.grid).syncSplits) == null ? void 0 : _b2.call(_a5, (other) => other.subGrids[me.region] && (other.subGrids[me.region].width = width));
    }
  }
  get width() {
    return super.width;
  }
  /**
   * Get/set SubGrid flex, which also sets header and footer flex (if available).
   * @property {Number|String}
   */
  set flex(flex) {
    var _a5, _b2;
    const me = this;
    me.hasCalculatedWidth = false;
    me.header.flex = flex;
    me.footer.flex = flex;
    super.flex = flex;
    if (me.isPainted) {
      me.onElementResize();
    }
    if (!me.isExpanding && !me.isCollapsing && !me.isConfiguring) {
      (_b2 = (_a5 = me.grid).syncSplits) == null ? void 0 : _b2.call(_a5, (other) => other.subGrids[me.region] && (other.subGrids[me.region].flex = flex));
    }
  }
  get flex() {
    return super.flex;
  }
  /**
   * Called when grid changes size. SubGrid determines if it has changed size and triggers scroll (for virtual
   * rendering in cells to work when resizing etc.)
   * @private
   */
  onInternalResize(element, newWidth, newHeight, oldWidth, oldHeight) {
    var _a5;
    const me = this, { grid } = me;
    super.onInternalResize(...arguments);
    if (grid == null ? void 0 : grid.isPainted) {
      me.syncSplitterButtonPosition();
      if (newWidth !== oldWidth) {
        me.trigger("beforeInternalResize", me);
        (_a5 = grid.beforeSubGridInternalResize) == null ? void 0 : _a5.call(grid, me);
        grid.trigger("horizontalScroll", {
          grid,
          subGrid: me,
          scrollLeft: me.scrollable.scrollLeft,
          scrollX: me.scrollable.x
        });
        grid.trigger("horizontalScrollEnd", { subGrid: me, scrollLeft: me.scrollable.scrollLeft });
        me.fakeScroller && me.refreshFakeScroll();
        grid.syncFlexedSubCols();
        me.fixRowWidthsInSafariEdge();
      }
      if (newHeight !== oldHeight) {
        grid.onHeightChange();
      }
      me.trigger("afterInternalResize", me);
    }
  }
  /**
   * Keeps the parallel splitters in the header, footer and fake scroller synced in terms
   * of being collapsed or not.
   * @private
   */
  syncParallelSplitters(collapsed) {
    const me = this, { grid } = me;
    if (me.splitterElement && me.$showingSplitter) {
      me.toggleSplitterCls("b-grid-splitter-collapsed", collapsed);
    } else {
      const prevGrid = grid.getSubGrid(grid.getPreviousRegion(me.region));
      if (prevGrid && prevGrid.splitterElement) {
        prevGrid.syncParallelSplitters(collapsed);
      }
    }
  }
  onSplitterMouseEnter() {
    const me = this, { nextSibling } = me;
    if (!me.collapsed && (!nextSibling || !nextSibling.collapsed)) {
      me.toggleSplitterCls("b-hover");
    }
    me.startSplitterButtonSyncing();
  }
  onSplitterMouseLeave() {
    const me = this, { nextSibling } = me;
    me.toggleSplitterCls("b-hover", false);
    if (!me.collapsed && (!nextSibling || !nextSibling.collapsed)) {
      me.stopSplitterButtonSyncing();
    }
  }
  startSplitterButtonSyncing() {
    const me = this;
    if (me.splitterElement) {
      me.syncSplitterButtonPosition();
      if (!me.splitterSyncScrollListener) {
        me.splitterSyncScrollListener = me.grid.scrollable.ion({
          scroll: "syncSplitterButtonPosition",
          thisObj: me
        });
      }
    }
  }
  stopSplitterButtonSyncing() {
    if (this.splitterSyncScrollListener) {
      this.splitterSyncScrollListener();
      this.splitterSyncScrollListener = null;
    }
  }
  syncSplitterButtonPosition() {
    const { grid } = this;
    this.splitterButtons.style.top = `${grid.scrollable.y + (grid.bodyHeight - (this.headerSplitter ? grid.headerHeight : 0)) / 2}px`;
  }
  /**
   * Get the "viewport" for the SubGrid as a Rectangle
   * @property {Core.helper.util.Rectangle}
   * @readonly
   */
  get viewRectangle() {
    const { scrollable } = this;
    return new Rectangle(scrollable.x, scrollable.y, this.width || 0, this.rowManager.viewHeight);
  }
  /**
   * Called when updating column widths to apply 'b-has-flex' which is used when fillLastColumn is configured.
   * @internal
   */
  updateHasFlex() {
    this.scrollable.element.classList.toggle("b-has-flex", this.columns.hasFlex);
  }
  updateResizable(resizable) {
    this.splitterElements.forEach((splitter) => DomHelper.toggleClasses(splitter, ["b-disabled"], !resizable));
  }
  /**
   * Resize all columns in the SubGrid to fit their width, according to their configured
   * {@link Grid.column.Column#config-fitMode}
   */
  resizeColumnsToFitContent() {
    this.grid.beginGridMeasuring();
    this.columns.visibleColumns.forEach((column) => {
      column.resizeToFitContent(null, null, true);
    });
    this.grid.endGridMeasuring();
  }
  //endregion
  //region Scroll
  get overflowingHorizontally() {
    return !this.collapsed && (this.grid.hideHeaders ? this.scrollable.hasOverflow("x") : this.header.scrollable.hasOverflow("x"));
  }
  get overflowingVertically() {
    return false;
  }
  /**
   * Fixes widths of fake scrollers
   * @private
   */
  refreshFakeScroll() {
    const me = this, {
      element,
      virtualScrollerElement,
      virtualScrollerWidth,
      header,
      footer,
      scrollable
    } = me, totalFixedWidth = [...header.contentElement.children].reduce(sumWidths, 0);
    scrollable.scrollWidth = totalFixedWidth;
    element.style.setProperty("--total-column-width", `${me.headerScrollWidth}px`);
    virtualScrollerElement.style.width = element.style.width;
    virtualScrollerElement.style.flex = element.style.flex;
    virtualScrollerElement.style.minWidth = element.style.minWidth;
    virtualScrollerElement.style.maxWidth = element.style.maxWidth;
    header.scrollable.syncOverflowState();
    footer.scrollable.syncOverflowState();
    if (!me.collapsed) {
      if (me.overflowingHorizontally) {
        virtualScrollerWidth.style.width = `${scrollable.scrollWidth || 0}px`;
        me.grid.virtualScrollers.classList.remove("b-hide-display");
      } else {
        virtualScrollerWidth.style.width = 0;
      }
    }
  }
  /**
   * Init scroll syncing for header and footer (if available).
   * @private
   */
  initScroll() {
    const me = this, {
      scrollable,
      virtualScrollerElement,
      grid
    } = me;
    if (!me.scrollInitialized) {
      me.fakeScroller = new Scroller({
        element: virtualScrollerElement,
        overflowX: true,
        widget: me
        // To avoid more expensive style lookup for RTL
      });
      me.scrollInitialized = true;
      scrollable.ion({
        scroll: "onSubGridScroll",
        scrollend: "onSubGridScrollEnd",
        thisObj: me
      });
      if (!grid.hideHorizontalScrollbar) {
        scrollable.addPartner(me.fakeScroller, "x");
        me.refreshFakeScroll();
      }
      if (!grid.hideHeaders) {
        scrollable.addPartner(me.header.scrollable, "x");
      }
      if (!grid.hideFooters) {
        scrollable.addPartner(me.footer.scrollable, "x");
      }
    }
  }
  onSubGridScrollEnd(event) {
    const me = this, { grid } = me;
    me.scrollingToCenter = event == null ? void 0 : event.scrollingToCenter;
    me.scrolling = false;
    me.handleHorizontalScroll(false);
    if (!DomHelper.scrollBarWidth) {
      grid.virtualScrollers.classList.remove("b-scrolling");
      me.hideOverlayScroller();
    }
    grid.trigger("horizontalScrollEnd", { subGrid: me });
  }
  onSubGridScroll(event) {
    const me = this;
    me.scrollingToCenter = event == null ? void 0 : event.scrollingToCenter;
    me.handleHorizontalScroll();
  }
  showOverlayScroller() {
    this.hideOverlayScroller.cancel();
    this.virtualScrollerElement.classList.add("b-show-virtual-scroller");
  }
  // Buffered 1500ms, hides virtual scrollers after scrolling has ended
  hideOverlayScroller() {
    this.virtualScrollerElement.classList.remove("b-show-virtual-scroller");
  }
  set scrolling(scrolling) {
    this._scrolling = scrolling;
  }
  get scrolling() {
    return this._scrolling;
  }
  /**
   * Triggers the 'horizontalScroll' event + makes sure overlay scrollbar is reachable with pointer for a substantial
   * amount of time after scrolling starts
   * @internal
   */
  handleHorizontalScroll(addCls = true) {
    const subGrid = this, { grid } = subGrid;
    if (!subGrid.scrolling && addCls) {
      subGrid.scrolling = true;
      if (!DomHelper.scrollBarWidth) {
        grid.virtualScrollers.classList.add("b-scrolling");
        subGrid.showOverlayScroller();
      }
    }
    grid.trigger("horizontalScroll", {
      subGrid,
      grid,
      scrollLeft: subGrid.scrollable.scrollLeft,
      scrollX: subGrid.scrollable.x,
      scrollingToCenter: subGrid == null ? void 0 : subGrid.scrollingToCenter
    });
  }
  /**
   * Scrolls a column into view (if it is not already). Called by Grid#scrollColumnIntoView, use it instead to not
   * have to care about which SubGrid contains a column.
   * @param {Grid.column.Column|String|Number} column Column name (data) or column index or actual column object.
   * @param {BryntumScrollOptions} [options] How to scroll.
   * @returns {Promise} If the column exists, a promise which is resolved when the column header element has been
   * scrolled into view.
   */
  scrollColumnIntoView(column, options) {
    const { columns, header } = this, scroller = header.scrollable;
    column = column instanceof Column ? column : columns.get(column) || columns.getById(column) || columns.getAt(column);
    if (column) {
      const columnHeaderElement = header.getHeader(column.id);
      if (columnHeaderElement) {
        return scroller.scrollIntoView(Rectangle.from(columnHeaderElement, null, true), options);
      }
    }
  }
  //endregion
  //region Rows
  /**
   * Creates elements for the new rows when RowManager has determined that more rows are needed
   * @private
   */
  onAddRow({ source = this.grid.rowManager, rows }) {
    var _a5;
    const me = this, { region } = me, config = me.rowElementConfig, frag = document.createDocumentFragment(), insertRowsBefore = (_a5 = source.rows[rows[rows.length - 1].index + 1]) == null ? void 0 : _a5.elements[region];
    rows.forEach((row) => {
      const rowElement = DomHelper.createElement(config);
      DomHelper.syncClassList(rowElement, row.cls);
      frag.appendChild(rowElement);
      row.addElement(region, rowElement);
      me.fixCellWidths(rowElement);
    });
    me.fixRowWidthsInSafariEdge();
    me.element.insertBefore(frag, insertRowsBefore || me.insertRowsBefore);
  }
  /**
   * Get all row elements for this SubGrid.
   * @property {HTMLElement[]}
   * @readonly
   */
  get rowElements() {
    return this.fromCache(".b-grid-row", true);
  }
  /**
   * Removes all row elements from the subgrids body and empties cache
   * @private
   */
  clearRows() {
    this.emptyCache();
    const all = this.element.querySelectorAll(".b-grid-row"), range = document.createRange();
    if (all.length) {
      range.setStartBefore(all[0]);
      range.setEndAfter(all[all.length - 1]);
      range.deleteContents();
    }
  }
  // only called when RowManager.rowScrollMode = 'dom', which is not intended to be used
  addNewRowElement() {
    const rowElement = DomHelper.append(this.element, this.rowElementConfig);
    this.fixCellWidths(rowElement);
    return rowElement;
  }
  get store() {
    return this.grid.store;
  }
  get rowManager() {
    var _a5;
    return (_a5 = this.grid) == null ? void 0 : _a5.rowManager;
  }
  //endregion
  // region Expand/collapse
  changeCollapsed(collapsed) {
    return Boolean(collapsed);
  }
  updateCollapsed(collapsed, was) {
    if (!this.isConfiguring) {
      this._fromPropertyUpdate = true;
      if (collapsed) {
        this.collapse();
      } else {
        this.expand();
      }
      this._fromPropertyUpdate = false;
    }
  }
  /**
   * Collapses subgrid. If collapsing subgrid is the only one expanded, next subgrid to the right (or previous) will
   * be expanded.
   *
   * ```javascript
   * let locked = grid.getSubGrid('locked');
   * locked.collapse().then(() => {
   *     console.log(locked.collapsed); // Logs 'True'
   * });
   *
   * let normal = grid.getSubGrid('normal');
   * normal.collapse().then(() => {
   *     console.log(locked.collapsed); // Logs 'False'
   *     console.log(normal.collapsed); // Logs 'True'
   * });
   * ```
   *
   * @param {Boolean} animate - Whether to animate the collapse.
   * @privateparam {Boolean} isRecursiveCall - Flag to set to true in case of partnered grids, to avoid infinite loop
   * @returns {Promise} A Promise which resolves when this SubGrid is fully collapsed.
   */
  async collapse(animate, isRecursiveCall = false) {
    const me = this, { grid } = me, { partners } = grid;
    animate = animate && Object.keys(grid.subGrids).length <= 2;
    if (grid.rendered && me._collapsed === true && !me._fromPropertyUpdate) {
      return;
    }
    if (grid.partners && !isRecursiveCall) {
      grid.isSyncingFromPartner = true;
      partners.forEach((partner) => partner.isSyncingFromPartner = true);
      await Promise.all([
        me.collapse(animate, true),
        ...partners.map((partner) => {
          var _a5;
          return (_a5 = partner.subGrids[me.region]) == null ? void 0 : _a5.collapse(animate, true);
        })
      ]);
      partners.forEach((partner) => partner.isSyncingFromPartner = false);
      grid.isSyncingFromPartner = false;
      return;
    }
    if (animate) {
      grid.element.classList.add("b-subgrid-width-transition");
      await grid.runWithTransition(() => me._collapseInternal(true), true);
      if (me.isDestroyed) {
        return;
      }
      grid.element.classList.remove("b-subgrid-width-transition");
    } else {
      me._collapseInternal(false);
    }
    grid.trigger("subGridCollapse", { subGrid: me });
    grid.afterToggleSubGrid({ subGrid: me, collapsed: true });
    me.isCollapsing = false;
    grid.trigger("afterInternalResize");
  }
  async _collapseInternal(animate = false) {
    var _a5, _b2;
    const me = this, {
      grid,
      element
    } = me, nextRegion = grid.getSubGrid(grid.getNextRegion(me.region)), splitterOwner = me.splitterElement ? me : me.previousSibling;
    let { _beforeCollapseState } = me, expandedRegions = 0;
    me.isCollapsing = true;
    grid.eachSubGrid((subGrid) => {
      subGrid !== me && !subGrid._collapsed && ++expandedRegions;
    });
    (_a5 = grid.syncSplits) == null ? void 0 : _a5.call(grid, (other) => {
      var _a6;
      return (_a6 = other.subGrids[me.region]) == null ? void 0 : _a6.collapse(animate);
    });
    if (expandedRegions === 0) {
      if (!nextRegion) {
        return;
      }
      await nextRegion.expand();
    }
    let widthChanged = false;
    if (!_beforeCollapseState) {
      _beforeCollapseState = me._beforeCollapseState = {};
      if (me.width) {
        widthChanged = true;
      }
      _beforeCollapseState.width = me.width;
      _beforeCollapseState.elementWidth = element.style.width;
      if (element.style.flex !== "") {
        _beforeCollapseState.flex = element.style.flex;
        me.header.element.style.flex = element.style.flex = "";
      }
      if (nextRegion.element.style.flex === "") {
        _beforeCollapseState.nextRegionWidth = nextRegion.width;
        nextRegion.width = "";
        nextRegion.flex = `1 1 ${_beforeCollapseState.nextRegionWidth}px`;
      }
      element.classList.add("b-grid-subgrid-collapsed");
      me.virtualScrollerElement.classList.add("b-collapsed");
      me.header.element.classList.add("b-collapsed");
      me.footer.element.classList.add("b-collapsed");
      me._collapsed = true;
      me.width = "";
      if (!widthChanged) {
        me.syncParallelSplitters(true);
      }
    }
    if (!me.isDestroyed) {
      if (widthChanged) {
        grid.refreshVirtualScrollbars();
        me.syncParallelSplitters(true);
        (_b2 = splitterOwner.startSplitterButtonSyncing) == null ? void 0 : _b2.call(splitterOwner);
      }
    }
  }
  /**
   * Expands subgrid.
   *
   * ```javascript
   * grid.getSubGrid('locked').expand().then(() => console.log('locked grid expanded'));
   * ```
   * @param {Boolean} animate - Whether to animate the expand.
   * @privateparam {Boolean} isRecursiveCall - Flag to set to true in case of partnered grids, to avoid infinite loop
   * @returns {Promise} A Promise which resolves when this SubGrid is fully expanded.
   */
  async expand(animate, isRecursiveCall = false) {
    const me = this, { grid } = me, { partners } = grid;
    animate = animate && Object.keys(grid.subGrids).length <= 2;
    if (grid.rendered && me._collapsed !== true && !me._fromPropertyUpdate) {
      return;
    }
    if (partners && !isRecursiveCall) {
      grid.isSyncingFromPartner = true;
      partners.forEach((partner) => partner.isSyncingFromPartner = true);
      await Promise.all([
        me.expand(animate, true),
        ...partners.map((partner) => {
          var _a5;
          return (_a5 = partner.subGrids[me.region]) == null ? void 0 : _a5.expand(animate, true);
        })
      ]);
      partners.forEach((partner) => partner.isSyncingFromPartner = false);
      grid.isSyncingFromPartner = false;
      return;
    }
    if (animate) {
      grid.element.classList.add("b-subgrid-width-transition");
      await grid.runWithTransition(() => me._expandInternal(true), true);
      if (me.isDestroyed) {
        return;
      }
      grid.element.classList.remove("b-subgrid-width-transition");
    } else {
      me._expandInternal(false);
    }
    me.columns.clearCaches();
    grid.trigger("subGridExpand", { subGrid: me });
    grid.afterToggleSubGrid({ subGrid: me, collapsed: false });
    me.isExpanding = false;
    grid.trigger("afterInternalResize");
  }
  async _expandInternal(animate = false) {
    var _a5;
    const me = this, {
      grid,
      _beforeCollapseState
    } = me, nextRegion = grid.getSubGrid(grid.getNextRegion(me.region)), splitterOwner = me.splitterElement ? me : me.previousSibling;
    me.isExpanding = true;
    (_a5 = grid.syncSplits) == null ? void 0 : _a5.call(grid, (other) => {
      var _a6;
      return (_a6 = other.subGrids[me.region]) == null ? void 0 : _a6.expand(animate);
    });
    if (_beforeCollapseState != null) {
      let widthChanged = false;
      if (me.width !== _beforeCollapseState.elementWidth) {
        widthChanged = true;
      }
      if (_beforeCollapseState.nextRegionWidth) {
        nextRegion.width = _beforeCollapseState.nextRegionWidth;
        nextRegion.flex = null;
      }
      me.element.classList.remove("b-grid-subgrid-collapsed");
      me._collapsed = false;
      me.virtualScrollerElement.classList.remove("b-collapsed");
      me.header.element.classList.remove("b-collapsed");
      me.footer.element.classList.remove("b-collapsed");
      if (_beforeCollapseState.flex) {
        me.width = _beforeCollapseState.width;
        me.header.flex = me.flex = _beforeCollapseState.flex;
        me.footer.flex = _beforeCollapseState.flex;
        me._width = null;
      } else {
        me.width = _beforeCollapseState.elementWidth;
      }
      if (widthChanged) {
        splitterOwner.stopSplitterButtonSyncing();
        me.syncParallelSplitters(false);
      }
      delete me._beforeCollapseState;
    }
  }
  hide() {
    var _a5, _b2;
    (_a5 = this.header) == null ? void 0 : _a5.hide();
    (_b2 = this.footer) == null ? void 0 : _b2.hide();
    this.hideSplitter();
    return super.hide();
  }
  show() {
    var _a5, _b2;
    const me = this;
    (_a5 = me.header) == null ? void 0 : _a5.show();
    (_b2 = me.footer) == null ? void 0 : _b2.show();
    if (me.region !== me.grid.regions[me.grid.regions.length - 1]) {
      me.showSplitter();
    }
    return super.show();
  }
  //endregion
};
//region Config
__publicField(SubGrid, "$name", "SubGrid");
// Factoryable type name
__publicField(SubGrid, "type", "subgrid");
/**
 * Region (name) for this SubGrid
 * @config {String} region
 */
/**
 * Column store, a store containing the columns for this SubGrid
 * @config {Grid.data.ColumnStore} columns
 */
__publicField(SubGrid, "defaultConfig", {
  insertRowsBefore: null,
  appendTo: null,
  monitorResize: true,
  headerClass: null,
  footerClass: null,
  /**
   * The subgrid "weight" determines its position among its SubGrid siblings.
   * Higher weights go further right.
   * @config {Number}
   * @category Layout
   */
  weight: null,
  defaultBindProperty: null,
  scrollable: {
    // Each SubGrid only handles scrolling in the X axis.
    // The owning Grid handles the Y axis.
    overflowX: "hidden-scroll"
  },
  scrollerClass: SubGridScroller,
  // Will be set to `true` by GridSubGrids if it calculates the subgrids width based on its columns.
  // Used to determine if hiding a column should affect subgrids width
  hasCalculatedWidth: null,
  /**
   * Set `true` to disable moving columns into or out of this SubGrid.
   * @config {Boolean}
   * @default false
   * @private
   */
  sealedColumns: null
});
__publicField(SubGrid, "configurable", {
  /**
   * @hideconfigs align appendTo content constrainTo draggable html height insertBefore insertFirst maxHeight scrollAction tag x y
   */
  /**
   * @hideproperties align appendTo content constrainTo draggable html insertBefore insertFirst maxHeight scrollAction tag x y
   */
  element: true,
  header: {},
  footer: {},
  virtualScrollerElement: true,
  splitterElement: true,
  headerSplitter: true,
  scrollerSplitter: true,
  footerSplitter: true,
  /**
   * Set `true` to start subgrid collapsed. To operate collapsed state on subgrid use
   * {@link #function-collapse}/{@link #function-expand} methods.
   * @prp {Boolean}
   * @default false
   */
  collapsed: false,
  /**
   * Set to `false` to prevent this subgrid being resized with the {@link Grid.feature.RegionResize} feature
   * @config {Boolean}
   * @default true
   */
  resizable: null,
  role: "rowgroup"
});
__publicField(SubGrid, "delayable", {
  // This uses a shorter delay for tests, see construct()
  hideOverlayScroller: 1e3
});
SubGrid.initClass();
SubGrid._$name = "SubGrid";

// ../Grid/lib/Grid/view/mixin/GridSubGrids.js
var GridSubGrids_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    static get properties() {
      return {
        /**
         * An object containing the {@link Grid.view.SubGrid} region instances, indexed by subGrid id ('locked', normal'...)
         * @member {Object<String,Grid.view.SubGrid>} subGrids
         * @readonly
         * @category Common
         */
        subGrids: {}
      };
    }
    //region Init
    changeSubGridConfigs(configs) {
      var _a6;
      const me = this, usedRegions = /* @__PURE__ */ new Set();
      for (const column of me.columns) {
        const { region } = column;
        if (region) {
          if (!configs[region]) {
            configs[region] = {};
          }
          usedRegions.add(region);
        }
      }
      if (configs.normal && ObjectHelper.isEmpty(configs.normal)) {
        configs.normal = { flex: 1 };
      }
      if ((_a6 = configs.normal) == null ? void 0 : _a6.width) {
        delete configs.normal.flex;
      }
      for (const region of usedRegions) {
        me.createSubGrid(region, configs[region]);
      }
      me.items = me.subGrids;
      return configs;
    }
    createSubGrid(region, config = null) {
      const me = this, subGridColumns = me.columns.chain((column) => column.region === region, ["region"]), subGridConfig = SubGrid.mergeConfigs({
        type: "subgrid",
        id: `${me.id}-${region}Subgrid`,
        parent: me,
        grid: me,
        region,
        headerClass: me.headerClass,
        footerClass: me.footerClass,
        columns: subGridColumns,
        // Sort by region unless weight is explicitly defined
        weight: region,
        // SubGridScrollers know about the main grid's scroller so that if asked to
        // scroll vertically they know how to do it.
        scrollable: {
          yScroller: me.scrollable
        }
      }, config || me.subGridConfigs[region]);
      let hasCalculatedWidth = false;
      if (!subGridConfig.flex && !subGridConfig.width && subGridConfig.flex !== 0 && subGridConfig.width !== 0) {
        subGridConfig.width = subGridColumns.totalFixedWidth;
        hasCalculatedWidth = true;
      }
      const subGrid = me.subGrids[region] = SubGrid.create(subGridConfig);
      subGrid.element.style.setProperty("--total-column-width", subGridColumns.totalFixedWidth);
      subGrid.hasCalculatedWidth = hasCalculatedWidth;
      if (region === me.regions[0]) {
        subGrid.isFirstRegion = true;
      }
      return subGrid;
    }
    // A SubGrid is added to Grid, add its header etc. too
    onChildAdd(subGrid) {
      if (subGrid.isSubGrid) {
        const me = this, {
          items: items2,
          headerContainer,
          virtualScrollers,
          footerContainer
        } = me, index = items2.indexOf(subGrid) * 2;
        if (!me.hideHeaders || me.columns.some((c) => c.flex)) {
          DomHelper.insertAt(headerContainer, subGrid.headerSplitter, index);
          DomHelper.insertAt(headerContainer, subGrid.header.element, index);
        }
        DomHelper.insertAt(virtualScrollers, subGrid.scrollerSplitter, index);
        DomHelper.insertAt(virtualScrollers, subGrid.virtualScrollerElement, index);
        DomHelper.insertAt(footerContainer, subGrid.footerSplitter, index);
        DomHelper.insertAt(footerContainer, subGrid.footer.element, index);
        items2.forEach((subGrid2, i) => {
          if (i < items2.length - 1) {
            subGrid2.showSplitter();
          }
        });
        if (index === 0 && me.emptyTextEl) {
          subGrid.element.appendChild(me.emptyTextEl);
        }
      }
      return super.onChildAdd(subGrid);
    }
    // A SubGrid is remove from grid, remove its header etc. too
    onChildRemove(subGrid) {
      super.onChildRemove(subGrid);
      if (subGrid.isSubGrid) {
        const { items: items2 } = this;
        delete this.subGrids[subGrid.region];
        ArrayHelper.remove(this.regions, subGrid.region);
        subGrid.destroy();
        if (items2.length) {
          items2[items2.length - 1].hideSplitter();
        }
      }
    }
    doDestroy() {
      this.eachSubGrid((subGrid) => subGrid.destroy());
      super.doDestroy();
    }
    //endregion
    //region Iteration & calling
    /**
     * Iterate over all subGrids, calling the supplied function for each.
     * @param {Function} fn Function to call for each instance
     * @param {Object} thisObj `this` reference to call the function in, defaults to the subGrid itself
     * @category SubGrid
     * @internal
     */
    eachSubGrid(fn2, thisObj = null) {
      this.items.forEach((subGrid, i) => {
        subGrid.isSubGrid && fn2.call(thisObj || subGrid, subGrid, i++);
      });
    }
    /**
     * Call a function by name for all subGrids (that have the function).
     * @param {String} fnName Name of function to call, uses the subGrid itself as `this` reference
     * @param params Parameters to call the function with
     * @returns {*} Return value from first SubGrid is returned
     * @category SubGrid
     * @internal
     */
    callEachSubGrid(fnName, ...params) {
      let returnValue = null;
      this.items.forEach((subGrid, i) => {
        if (subGrid.isSubGrid && subGrid[fnName]) {
          const partialReturnValue = subGrid[fnName](...params);
          if (i === 0)
            returnValue = partialReturnValue;
        }
      });
      return returnValue;
    }
    //endregion
    //region Getters
    get regions() {
      return this.items.map((item) => item.region);
    }
    /**
     * This method should return names of the two last regions in the grid as they are visible in the UI. In case
     * `regions` property cannot be trusted, use different approach. Used by SubGrid and RegionResize to figure out
     * which region should collapse or expand.
     * @returns {String[]}
     * @private
     * @category SubGrid
     */
    getLastRegions() {
      const result = this.regions.slice(-2);
      return result.length === 2 ? result : [result[0], result[0]];
    }
    /**
     * This method should return right neighbour for passed region, or left neighbour in case last visible region is passed.
     * This method is used to decide which subgrid should take space of the collapsed one.
     * @param {String} region
     * @returns {String}
     * @private
     * @category SubGrid
     */
    getNextRegion(region) {
      const regions = this.regions;
      return regions[regions.indexOf(region) + 1] || regions[regions.length - 2];
    }
    getPreviousRegion(region) {
      return this.regions[this.regions.indexOf(region) - 1];
    }
    /**
     * Returns the subGrid for the specified region.
     * @param {String} region Region, eg. locked or normal (per default)
     * @returns {Grid.view.SubGrid} A subGrid
     * @category SubGrid
     */
    getSubGrid(region) {
      return this.subGrids[region];
    }
    /**
     * Get the SubGrid that contains specified column
     * @param {String|Grid.column.Column} column Column "name" or column object
     * @returns {Grid.view.SubGrid}
     * @category SubGrid
     */
    getSubGridFromColumn(column) {
      column = column instanceof Column ? column : this.columns.getById(column) || this.columns.get(column);
      return this.getSubGrid(column.region);
    }
    //endregion
    /**
     * Returns splitter element for subgrid
     * @param {Grid.view.SubGrid|String} subGrid
     * @returns {HTMLElement}
     * @private
     * @category SubGrid
     */
    resolveSplitter(subGrid) {
      const regions = this.getLastRegions();
      let region = subGrid instanceof SubGrid ? subGrid.region : subGrid;
      if (regions[1] === region) {
        region = regions[0];
      }
      return this.subGrids[region].splitterElement;
    }
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "GridSubGrids"), _a5;
};

// ../Grid/lib/Grid/localization/En.js
var emptyString2 = new String();
var locale3 = {
  localeName: "En",
  localeDesc: "English (US)",
  localeCode: "en-US",
  localeRtl: false,
  ChecklistFilterCombo: {
    search: "Search",
    selectAll: "Select All",
    removeAll: "Remove All",
    filtersSet: (n) => n === 1 ? `1 filter set` : `${n} filters set`,
    noItems: "No results found",
    placeholder: "Filter...",
    apply: "Apply"
  },
  ColumnPicker: {
    column: "Column",
    columnsMenu: "Columns",
    hideColumn: "Hide column",
    hideColumnShort: "Hide",
    newColumns: "New columns",
    showName: (column) => `Show column${column.text ? ` ${column.text}` : ""}`,
    hideName: (column) => `Hide column${column.text ? ` ${column.text}` : ""}`
  },
  Filter: {
    applyFilter: "Apply filter",
    filter: "Filter",
    editFilter: "Edit filter",
    on: "On",
    before: "Before",
    after: "After",
    equals: "Equals",
    lessThan: "Less than",
    moreThan: "More than",
    removeFilter: "Remove filter",
    disableFilter: "Disable filter"
  },
  FilterBar: {
    enableFilterBar: "Show filter bar",
    disableFilterBar: "Hide filter bar"
  },
  Group: {
    group: "Group",
    groupAscending: "Group ascending",
    groupDescending: "Group descending",
    groupAscendingShort: "Ascending",
    groupDescendingShort: "Descending",
    stopGrouping: "Stop grouping",
    stopGroupingShort: "Stop"
  },
  HeaderMenu: {
    moveBefore: (text) => `Move before "${text}"`,
    moveAfter: (text) => `Move after "${text}"`,
    collapseColumn: "Collapse column",
    expandColumn: "Expand column"
  },
  ColumnRename: {
    rename: "Rename"
  },
  MergeCells: {
    mergeCells: "Merge cells",
    menuTooltip: "Merge cells with same value when sorted by this column"
  },
  Search: {
    searchForValue: "Search for value"
  },
  Sort: {
    sort: "Sort",
    sortAscending: "Sort ascending",
    sortDescending: "Sort descending",
    multiSort: "Multi sort",
    removeSorter: "Remove sorter",
    addSortAscending: "Add ascending sorter",
    addSortDescending: "Add descending sorter",
    toggleSortAscending: "Change to ascending",
    toggleSortDescending: "Change to descending",
    sortAscendingShort: "Ascending",
    sortDescendingShort: "Descending",
    removeSorterShort: "Remove",
    addSortAscendingShort: "+ Ascending",
    addSortDescendingShort: "+ Descending"
  },
  Split: {
    split: "Split",
    unsplit: "Unsplit",
    horizontally: "Horizontally",
    vertically: "Vertically",
    both: "Both"
  },
  LockRows: {
    lockRow: "Lock row",
    unlockRow: "Unlock row"
  },
  ActionColumn: {
    rowActions: "Row actions"
  },
  Column: {
    columnLabel: (column) => `${column.text ? `${column.text} column. ` : ""}SPACE for context menu${column.sortable ? ", ENTER to sort" : ""}`,
    cellLabel: emptyString2
  },
  WidgetColumn: {
    rowWidgets: "Row widgets"
  },
  Checkbox: {
    toggleRowSelect: "Toggle row selection",
    toggleSelection: "Toggle selection of entire dataset",
    toggleName: (column) => `Toggle ${column.text || "value"}`
  },
  RatingColumn: {
    cellLabel: (column) => {
      var _a5;
      return `${column.text ? column.text : ""} ${((_a5 = column.location) == null ? void 0 : _a5.record) ? `rating : ${column.location.record.get(column.field) || 0}` : ""}`;
    }
  },
  RowNumberColumn: {
    rowNumber: "Row number"
  },
  GridBase: {
    loadFailedMessage: "Data loading failed!",
    syncFailedMessage: "Data synchronization failed!",
    unspecifiedFailure: "Unspecified failure",
    networkFailure: "Network error",
    parseFailure: "Failed to parse server response",
    serverResponse: "Server response:",
    noRows: "No records to display",
    moveColumnLeft: "Move to left section",
    moveColumnRight: "Move to right section",
    moveColumnTo: (region) => `Move column to ${region}`,
    filteredRecords: (n) => n ? n === 1 ? `1 row matches filter` : `${n} rows match filter` : "No rows match filter"
  },
  CellMenu: {
    removeRow: "Delete"
  },
  RowCopyPaste: {
    copyRecord: "Copy",
    cutRecord: "Cut",
    pasteRecord: "Paste",
    rows: "rows",
    row: "row"
  },
  CellCopyPaste: {
    copy: "Copy",
    cut: "Cut",
    paste: "Paste"
  },
  PdfExport: {
    "Waiting for response from server": "Waiting for response from server...",
    "Export failed": "Export failed",
    "Server error": "Server error",
    "Generating pages": "Generating pages...",
    "Click to abort": "Cancel"
  },
  ExportDialog: {
    width: "40em",
    labelWidth: "12em",
    exportSettings: "Export settings",
    export: "Export",
    printSettings: "Print settings",
    print: "Print",
    exporterType: "Control pagination",
    cancel: "Cancel",
    fileFormat: "File format",
    rows: "Rows",
    alignRows: "Align rows",
    columns: "Columns",
    paperFormat: "Paper format",
    orientation: "Orientation",
    repeatHeader: "Repeat header"
  },
  ExportRowsCombo: {
    all: "All rows",
    visible: "Visible rows"
  },
  ExportOrientationCombo: {
    portrait: "Portrait",
    landscape: "Landscape"
  },
  SinglePageExporter: {
    singlepage: "Single page"
  },
  MultiPageExporter: {
    multipage: "Multiple pages",
    exportingPage: ({ currentPage, totalPages }) => `Exporting page ${currentPage}/${totalPages}`
  },
  MultiPageVerticalExporter: {
    multipagevertical: "Multiple pages (vertical)",
    exportingPage: ({ currentPage, totalPages }) => `Exporting page ${currentPage}/${totalPages}`
  },
  RowExpander: {
    loading: "Loading",
    expand: "Expand row details",
    collapse: "Collapse row details",
    columnDescription: "Toggle row details"
  },
  TreeGroup: {
    group: "Group by",
    stopGrouping: "Stop grouping",
    stopGroupingThisColumn: "Ungroup column"
  }
};
var En_default2 = LocaleHelper.publishLocale(locale3);

// ../Grid/lib/Grid/view/GridBase.js
var resolvedPromise = new Promise((resolve) => resolve());
var storeListenerName2 = "GridBase:store";
var defaultScrollOptions3 = {
  block: "nearest",
  inline: "nearest"
};
var datasetReplaceActions = {
  dataset: 1,
  pageLoad: 1,
  filter: 1
};
var fullRefreshAfterUpdateFields = {
  hidden: 1,
  mergeCells: 1,
  region: 1
};
var emptyArray11 = Object.freeze([]);
var ascending = (l, r) => l - r;
var GridBase = class extends Panel.mixin(
  Pluggable_default,
  State_default,
  GridElementEvents_default,
  GridFeatures_default,
  GridNavigation_default,
  GridResponsive_default,
  GridSelection_default,
  GridState_default,
  GridSubGrids_default,
  LoadMaskable_default
) {
  constructor() {
    super(...arguments);
    //endregion
    __publicField(this, "_animationSuspendedCounter", 0);
  }
  static get delayable() {
    return {
      // These use a shorter delay for tests, see finishConfigure()
      bufferedAfterColumnsResized: 250,
      bufferedElementResize: 250
    };
  }
  static get configurable() {
    return {
      //region Hidden configs
      /**
       * @hideconfigs autoUpdateRecord, defaults, hideWhenEmpty, itemCls, items, layout, layoutStyle, lazyItems,
       * namedItems, record, textContent, defaultAction, html, htmlCls, tag, textAlign, trapFocus, content,
       * defaultBindProperty, defaultFocus, align, anchor, centered, constrainTo, draggable, floating,
       * hideAnimation, positioned, scrollAction, showAnimation, x, y, localeClass, localizableProperties,
       * showTooltipWhenDisabled, tooltip, strictRecordMapping, maximizeOnMobile
       */
      /**
       * @hideproperties html, isSettingValues, isValid, items, record, values, content, layoutStyle, firstItem,
       * lastItem, anchorSize, x, y, layout, strictRecordMapping, visibleChildCount, maximizeOnMobile
       */
      /**
       * @hidefunctions attachTooltip, add, getWidgetById, insert, processWidgetConfig, remove, removeAll, getAt,
       * alignTo, setXY, showBy, showByPoint, toFront
       */
      //endregion
      /**
       * Set to `true` to make the grid read-only, by disabling any UIs for modifying data.
       *
       * __Note that checks MUST always also be applied at the server side.__
       * @prp {Boolean} readOnly
       * @default false
       * @category Misc
       */
      /**
       * Automatically set grids height to fit all rows (no scrolling in the grid). In general you should avoid
       * using `autoHeight: true`, since it will bypass Grids virtual rendering and render all rows at once, which
       * in a larger grid is really bad for performance.
       * @config {Boolean}
       * @default false
       * @category Layout
       */
      autoHeight: null,
      disableAutoHeightWarning: null,
      disableNoIDWarning: null,
      /**
       * Configure this as `true` to allow elements within cells to be styled as `position: sticky`.
       *
       * Columns which contain sticky content will need to be configured with
       *
       * ```javascript
       *    cellCls : 'b-sticky-cell',
       * ```
       *
       * Or a custom renderer can add the class to the passed cell element.
       *
       * It is up to the application author how to style the cell content. It is recommended that
       * a custom renderer create content with CSS class names which the application author
       * will use to apply the `position`, and matching `margin-top` and `top` styles to keep the
       * content stuck at the grid's top.
       *
       * Note that not all browsers support this CSS feature. A cross browser alternative
       * is to use the {link Grid.feature.StickyCells StickyCells} Feature.
       * @config {Boolean}
       * @category Misc
       */
      enableSticky: null,
      /**
       * Set to `true` to allow text selection in the grid cells. Note, this cannot be used simultaneously with the
       * `RowReorder` feature.
       * @config {Boolean}
       * @default false
       * @category Selection
       */
      enableTextSelection: null,
      /**
       * Set to `true` to stretch the last column in a grid with all fixed width columns
       * to fill extra available space if the grid's width is wider than the sum of all
       * configured column widths.
       * @config {Boolean}
       * @default
       * @category Layout
       */
      fillLastColumn: true,
      /**
       * See {@link Grid.view.Grid#keyboard-shortcuts Keyboard shortcuts} for details
       * @config {Object<String,KeyMapConfig>} keyMap
       * @category Common
       */
      positionMode: "translate",
      // translate, translate3d, position
      /**
       * Configure as `true` to have the grid show a red "changed" tag in cells whose
       * field value has changed and not yet been committed.
       *
       * Set `showDirty.duringEdit` to `true` to show the red tag while editing a cell
       * ```javascript
       * showDirty : {
       *     duringEdit : true
       * }
       * ```
       *
       * Set `showDirty.newRecord` to `true` to show the red tag for all the cells of the
       * new record
       *
       * ```javascript
       * showDirty : {
       *     newRecord : true
       * }
       * ```
       *
       * @config {Boolean|Object}
       * @property {Boolean} showDirty.duringEdit Set to `true` to show the red tag while editing a cell
       * @property {Boolean} showDirty.newRecord Set to `true` to show the red tag for all the cells of the new record
       * @default false
       * @category Misc
       */
      showDirty: null,
      /**
       * An object containing sub grid configuration objects keyed by a `region` property.
       * By default, grid has a 'locked' region (if configured with locked columns) and a 'normal' region.
       * The 'normal' region defaults to use `flex: 1`.
       *
       * This config can be used to reconfigure the "built-in" sub grids or to define your own.
       *
       * Redefining the default regions:
       *
       * {@frameworktabs}
       * {@js}
       * ```javascript
       * new Grid({
       *   subGridConfigs : {
       *     locked : { flex : 1 },
       *     normal : { width : 100 }
       *   }
       * });
       * ```
       * {@endjs}
       * {@react}
       * ```javascript
       * const App = props => {
       *     const subGridConfigs = {
       *         locked : { flex : 1 },
       *         normal : { width : 100 }
       *     };
       *
       *     return <bryntum-grid subGridConfigs={subGridConfigs} />
       * }
       * ```
       * {@endreact}
       * {@vue}
       * ```html
       * <bryntum-grid :sub-grid-configs="subGridConfigs" />
       * ```
       * ```javascript
       * export default {
       *     setup() {
       *         return {
       *             subGridConfigs : [
       *                 locked : { flex : 1 },
       *                 normal : { width : 100 }
       *             ]
       *         };
       *     }
       * }
       * ```
       * {@endvue}
       * {@angular}
       * ```html
       * <bryntum-grid [subGridConfigs]="subGridConfigs"></bryntum-grid>
       * ```
       * ```typescript
       * export class AppComponent {
       *      subGridConfigs = [
       *          locked : { flex : 1 },
       *          normal : { width : 100 }
       *      ]
       *  }
       * ```
       * {@endangular}
       * {@endframeworktabs}
       *
       * Defining your own multi region grid:
       *
       * ```javascript
       * new Grid({
       *   subGridConfigs : {
       *     left   : { width : 100 },
       *     middle : { flex : 1 },
       *     right  : { width  : 100 }
       *   },
       *
       *   columns : [
       *     { field : 'manufacturer', text: 'Manufacturer', region : 'left' },
       *     { field : 'model', text: 'Model', region : 'middle' },
       *     { field : 'year', text: 'Year', region : 'middle' },
       *     { field : 'sales', text: 'Sales', region : 'right' }
       *   ]
       * });
       * ```
       * @config {Object<String,SubGridConfig>}
       * @category Misc
       */
      subGridConfigs: {
        normal: { flex: 1 }
      },
      /**
       * Store that holds records to display in the grid, or a store config object. If the configuration contains
       * a `readUrl`, an `AjaxStore` will be created.
       *
       * Note that a store will be created during initialization if none is specified.
       *
       * Supplying a store config object at initialization time:
       *
       * ```javascript
       * const grid = new Grid({
       *     store : {
       *         fields : ['name', 'powers'],
       *         data   : [
       *             { id : 1, name : 'Aquaman', powers : 'Decent swimmer' },
       *             { id : 2, name : 'Flash', powers : 'Pretty fast' },
       *         ]
       *     }
       * });
       * ```
       *
       * Accessing the store at runtime:
       *
       * ```javascript
       * grid.store.sort('powers');
       * ```
       *
       * @prp {Core.data.Store}
       * @accepts {Core.data.Store|StoreConfig}
       * @typings {Core.data.Store|StoreConfig|Core.data.AjaxStore|AjaxStoreConfig}
       * @category Common
       */
      store: {
        value: {},
        $config: "nullify"
      },
      rowManager: {
        value: {},
        $config: ["nullify", "lazy"]
      },
      /**
       * Configuration values for the {@link Core.util.ScrollManager} class on initialization. Returns the
       * {@link Core.util.ScrollManager} at runtime.
       *
       * @prp {Core.util.ScrollManager}
       * @accepts {ScrollManagerConfig|Core.util.ScrollManager}
       * @readonly
       * @category Scrolling
       */
      scrollManager: {
        value: {},
        $config: ["nullify", "lazy"]
      },
      /**
       * Accepts column definitions for the grid during initialization. They will be used to create
       * {@link Grid/column/Column} instances that are added to a {@link Grid/data/ColumnStore}.
       *
       * At runtime it returns the {@link Grid/data/ColumnStore}.
       *
       * Initialization using column config objects:
       *
       * ```javascript
       * new Grid({
       *   columns : [
       *     { text : 'Alias', field : 'alias' },
       *     { text : 'Superpower', field : 'power' }
       *   ]
       * });
       * ```
       *
       * Also accepts a store config object:
       *
       * ```javascript
       * new Grid({
       *   columns : {
       *     data : [
       *       { text : 'Alias', field : 'alias' },
       *       { text : 'Superpower', field : 'power' }
       *     ],
       *     listeners : {
       *       update() {
       *         // Some update happened
       *       }
       *     }
       *   }
       * });
       * ```
       *
       * Access the {@link Grid/data/ColumnStore} at runtime to manipulate columns:
       *
       * ```javascript
       * grid.columns.add({ field : 'column', text : 'New column' });
       * ```
       *
       * Replacing the columns fully at runtime:
       *
       * ```javascript
       * grid.columns = [{ field : 'column', text : 'New column' }];
       * ```
       * @prp {Grid.data.ColumnStore}
       * @accepts {Grid.data.ColumnStore|GridColumnConfig[]|ColumnStoreConfig}
       * @category Common
       */
      columns: {
        value: [],
        $config: "nullify"
      },
      /**
       * Grid's `min-height`. Defaults to `10em` to be sure that the Grid always has a height wherever it is
       * inserted.
       *
       * Can be either a String or a Number (which will have 'px' appended).
       *
       * Note that _reading_ the value will return the numeric value in pixels.
       *
       * @config {String|Number}
       * @category Layout
       */
      minHeight: "10em",
      /**
       * Set to `true` to hide the column header elements
       * @prp {Boolean}
       * @default false
       * @category Misc
       */
      hideHeaders: null,
      /**
       * Set to `true` to hide the footer elements
       * @prp {Boolean}
       * @default
       * @category Misc
       */
      hideFooters: true,
      /**
       * Set to `false` to crop text in grid cells without ellipsis (...). When enabled, cells containing pure
       * use `display : block`, instead of `display : flex` to allow ellipsis to work.
       * **NOTE** Only supported in browsers that support `:has()` CSS selector
       * @prp {Boolean}
       * @default
       * @category Misc
       */
      cellEllipsis: true,
      /**
       * Set to `true` to hide the Grid's horizontal scrollbar(s)
       * @config {Boolean}
       * @default false
       * @category Misc
       */
      hideHorizontalScrollbar: null,
      contentElMutationObserver: false,
      trapFocus: false,
      ariaElement: "bodyElement",
      cellTabIndex: -1,
      rowCls: {
        value: "b-grid-row",
        $config: {
          merge: this.mergeCls
        }
      },
      cellCls: {
        value: "b-grid-cell",
        $config: {
          merge: this.mergeCls
        }
      },
      /**
       * Text or HTML, or a {@link DomConfig} block to display when there is no data to display in the grid
       * @prp {String|DomConfig}
       * @default
       * @category Common
       */
      emptyText: "L{noRows}",
      sortFeatureStore: "$store",
      /**
       * Row height in pixels. This allows the default height for rows to be controlled. Note that it may be
       * overriden by specifying a {@link Grid/data/GridRowModel#field-rowHeight} on a per record basis, or from
       * a column {@link Grid/column/Column#config-renderer}.
       *
       * When initially configured as `null`, an empty row will be measured and its height will be used as default
       * row height, enabling it to be controlled using CSS
       *
       * @prp {Number}
       * @category Common
       */
      rowHeight: null,
      /**
       * Preserve the grid's vertical scroll position when changesets are applied, as in the case of remote
       * changes, or when stores are configured with {@link Core.data.Store#config-syncDataOnLoad}.
       *
       * @prp {PreserveScrollOptions|Boolean}
       * @default
       * @category Common
       */
      preserveScroll: false,
      /**
       * When the {@link Grid.feature.Tree} feature is in use and the Store is a tree store, this
       * config may be set to `true` to visually animate branch node expand and collapse operations.
       * {@note}This is not supported in Scheduler and Gantt{/@note}
       * @prp {Boolean}
       * @default true
       * @category Tree
       */
      animateTreeNodeToggle: !VersionHelper.isTestEnv,
      /**
       * Set to `false` to not show column lines. End result might be overruled by/differ between themes.
       *
       * @prp {Boolean}
       * @default
       * @category Misc
       */
      columnLines: true,
      /**
       * Set to `false` to not show row lines. End result might be overruled by/differ between themes.
       *
       * @prp {Boolean}
       * @default
       * @category Misc
       */
      rowLines: true,
      /**
       * Use fixed row height. Setting this to `true` will configure the underlying RowManager to use fixed row
       * height, which sacrifices the ability to use rows with variable height to gain a fraction better
       * performance.
       *
       * Using this setting also ignores the {@link Grid.view.GridBase#config-getRowHeight} function, and thus any
       * row height set in data. Only Grids configured {@link Grid.view.GridBase#config-rowHeight} is used.
       *
       * @config {Boolean}
       * @category Layout
       */
      fixedRowHeight: null,
      /**
       * An object which names formula prefixes which will be applied to all columns configured with
       * `formula : true`.
       *
       * Each entry is keyed by a formula prefix, and each value is how to instantiate and configure a
       * {@link Core.util.FormulaProvider} when that prefix is used in the typed vale, eg: `=XXX(`
       *
       * If the configured value contains a `type` property, that is used to determine a registered
       * formula provider subclass to instantiate.
       *
       * ```javascript
       * fields : [{
       *     name             : 'review',
       *     formulaProviders : {
       *        AI : {
       *          type : 'remote',
       *          url  : 'https://my-ai-service.com'
       *        },
       *        SUM : {
       *           type : 'sum'
       *        }
       *     }
       * }]
       * ```
       *
       * Formula providers may be added to dynamically:
       *
       * ```javascript
       * // Enable registered MYFormula class to be used as a formula provider in the Grid
       * grid.store.modelClass.fieldMap.review.formulaProviders.MY = { ... };
       * ```
       * @config {Object<String,FormulaProviderConfig>} formulaProviders
       */
      formulaProviders: null
    };
  }
  // Default settings, applied in grids constructor.
  static get defaultConfig() {
    return {
      /**
       * A function called for each row to determine its height. It is passed a {@link Core.data.Model record} and
       * expected to return the desired height of that records row. If the function returns a falsy value, Grids
       * configured {@link Grid.view.GridBase#config-rowHeight} is used.
       *
       * The default implementation of this function returns the row height from the records
       * {@link Grid.data.GridRowModel#field-rowHeight rowHeight field}.
       *
       * Override this function to take control over how row heights are determined:
       *
       * ```javascript
       * new Grid({
       *    getRowHeight(record) {
       *        if (record.low) {
       *            return 20;
       *        }
       *        else if (record.high) {
       *            return 60;
       *        }
       *
       *        // Will use grids configured rowHeight
       *        return null;
       *    }
       * });
       * ```
       *
       * NOTE: Height set in a Column renderer takes precedence over the height returned by this function.
       *
       * @config {Function} getRowHeight
       * @param {Core.data.Model} getRowHeight.record Record to determine row height for
       * @returns {Number} Desired row height
       * @category Layout
       */
      // used if no rowHeight specified and none found in CSS. not public since our themes have row height
      // specified and this is more of an internal failsafe
      defaultRowHeight: 45,
      /**
       * Refresh entire row when a record changes (`true`) or, if possible, only the cells affected (`false`).
       *
       * When this is set to `false`, then if a column uses a renderer, cells in that column will still
       * be updated because it is impossible to know whether the cells value will be affected.
       *
       * If a standard, provided Column class is used with no custom renderer, its cells will only be updated
       * if the column's {@link Grid.column.Column#config-field} is changed.
       * @config {Boolean}
       * @default
       * @category Misc
       */
      fullRowRefresh: true,
      /**
       * Specify `true` to preserve vertical scroll position after store actions that trigger a `refresh` event,
       * such as loading new data and filtering.
       * @config {Boolean}
       * @default false
       * @category Misc
       */
      preserveScrollOnDatasetChange: null,
      /**
       * True to preserve focused cell after loading new data
       * @config {Boolean}
       * @default
       * @category Misc
       */
      preserveFocusOnDatasetChange: true,
      /**
       * Convenient shortcut to set data in grids store both during initialization and at runtime. Can also be
       * used to retrieve data at runtime, although we do recommend interacting with Grids store instead using
       * the {@link #property-store} property.
       *
       * Setting initial data during initialization:
       *
       * ```javascript
       * const grid = new Grid({
       *     data : [
       *       { id : 1, name : 'Batman' },
       *       { id : 2, name : 'Robin' },
       *       ...
       *     ]
       * });
       * ```
       *
       * Setting data at runtime:
       *
       * ```javascript
       * grid.data = [
       *     { id : 3, name : 'Joker' },
       *     ...
       * ];
       * ```
       *
       * Getting data at runtime:
       *
       * ```javascript
       * const records = store.data;
       * ```
       *
       * Note that a Store will be created during initialization if none is specified.
       *
       * @prp {Core.data.Model[]}
       * @accepts {Object[]|Core.data.Model[]}
       * @non-lazy-load
       * @category Common
       */
      data: null,
      /**
       * Region to which columns are added when they have none specified
       * @config {String}
       * @default
       * @category Misc
       */
      defaultRegion: "normal",
      /**
       * Set to `true` to destroy the store when the grid is destroyed.
       * @config {Boolean}
       * @default false
       * @category Misc
       */
      destroyStore: null,
      /**
       * Grids change the `maskDefaults` to cover only their `body` element.
       * @config {MaskConfig}
       * @category Misc
       */
      maskDefaults: {
        cover: "body",
        target: "element"
      },
      /**
       * Set to `false` to only measure cell contents when double-clicking the edge between column headers.
       * @config {Boolean}
       * @default
       * @category Layout
       */
      resizeToFitIncludesHeader: true,
      /**
       * Set to `false` to prevent remove row animation and remove the delay related to that.
       * @config {Boolean}
       * @default
       * @category Misc
       */
      animateRemovingRows: true,
      /**
       * Set to `true` to animate row removals caused by filtering.
       * @config {Boolean}
       * @default false
       * @category Misc
       */
      animateFilterRemovals: null,
      /**
       * Set to `true` to not get a warning when using another base class than GridRowModel for your grid data. If
       * you do, and would like to use the full feature set of the grid then include the fields from GridRowModel
       * in your model definition.
       * @config {Boolean}
       * @default false
       * @category Misc
       */
      disableGridRowModelWarning: null,
      /**
       * Set to `true` to not get a warning when calling {@link #function-getState} when there is a column
       * configured without an `id`. But the recommended action is to always configure columns with an `id` when
       * using states.
       * @config {Boolean}
       * @default false
       * @category State
       */
      disableGridColumnIdWarning: null,
      headerClass: Header,
      footerClass: Footer,
      testPerformance: false,
      rowScrollMode: "move",
      // move, dom, all
      /**
       * Grid monitors window resize by default.
       * @config {Boolean}
       * @default true
       * @category Misc
       */
      monitorResize: true,
      /**
       * An object containing Feature configuration objects (or `true` if no configuration is required)
       * keyed by the Feature class name in all lowercase.
       * @config {Object}
       * @category Common
       */
      features: true,
      /**
       * Configures whether the grid is scrollable in the `Y` axis. This is used to configure a {@link Core.helper.util.Scroller}.
       * See the {@link #config-scrollerClass} config option.
       * @config {Boolean|ScrollerConfig|Core.helper.util.Scroller}
       * @category Scrolling
       */
      scrollable: {
        // Just Y for now until we implement a special grid.view.Scroller subclass
        // Which handles the X scrolling of subgrids.
        overflowY: true
      },
      /**
       * The class to instantiate to use as the {@link #config-scrollable}. Defaults to {@link Core.helper.util.Scroller}.
       * @config {Core.helper.util.Scroller}
       * @typings {typeof Scroller}
       * @category Scrolling
       */
      scrollerClass: GridScroller,
      refreshSuspended: 0,
      /**
       * Animation transition duration in milliseconds.
       * @prp {Number}
       * @default
       * @category Misc
       */
      transitionDuration: 500,
      /**
       * Event which is used to show context menus.
       * Available options are: 'contextmenu', 'click', 'dblclick'.
       * @config {'contextmenu'|'click'|'dblclick'}
       * @category Misc
       * @default
       */
      contextMenuTriggerEvent: "contextmenu",
      localizableProperties: ["emptyText"],
      asyncEventSuffix: "",
      fixElementHeightsBuffer: 350,
      testConfig: {
        transitionDuration: 50,
        fixElementHeightsBuffer: 50
      }
    };
  }
  static get properties() {
    return {
      _selectedRecords: [],
      _verticalScrollHeight: 0,
      virtualScrollHeight: 0,
      _scrollTop: null
    };
  }
  // Keep this commented out to have easy access to the syntax next time we need to use it
  // static get deprecatedEvents() {
  //     return {
  //         cellContextMenuBeforeShow : {
  //             product            : 'Grid',
  //             invalidAsOfVersion : '5.0.0',
  //             message            : '`cellContextMenuBeforeShow` event is deprecated, in favor of `cellMenuBeforeShow` event. Please see https://bryntum.com/products/grid/docs/guide/Grid/upgrades/4.0.0 for more information.'
  //         }
  //     };
  // }
  //endregion
  //region Init-destroy
  finishConfigure(config) {
    const me = this, { initScroll } = me;
    me.initScroll = () => !me.scrollInitialized && initScroll.call(me);
    if (VersionHelper.isTestEnv) {
      me.bufferedAfterColumnsResized.delay = 50;
      me.bufferedElementResize.delay = 50;
    }
    super.finishConfigure(config);
    LocaleManager_default.ion({
      locale: "onBeforeLocaleChange",
      prio: 1,
      thisObj: me
    });
    LocaleManager_default.ion({
      locale: "onLocaleChange",
      prio: -1,
      thisObj: me
    });
    GlobalEvents_default.ion({
      theme: "onThemeChange",
      thisObj: me
    });
    me.ion({
      subGridExpand: "internalOnSubGridExpand",
      prio: -1,
      thisObj: me
    });
    me.bufferedFixElementHeights = me.buffer("fixElementHeights", me.fixElementHeightsBuffer, me);
    me.setGridClassList(me.element.classList);
    me.verticalScroller.classList.remove("b-content-element", "b-auto-container");
    me.bodyWrapElement.classList.remove("b-auto-container-panel");
    if (CSS.supports("selector(:has(*))")) {
      me.element.classList.add("b-supports-has");
    }
  }
  internalOnSubGridExpand() {
    this.renderContents();
  }
  onBeforeLocaleChange() {
    this._suspendRenderContentsOnColumnsChanged = true;
  }
  onLocaleChange() {
    this._suspendRenderContentsOnColumnsChanged = false;
    if (this.isPainted) {
      this.renderContents();
    }
  }
  finalizeInit() {
    super.finalizeInit();
    if (this.store.isLoading) {
      this.onStoreBeforeRequest();
    }
  }
  changeScrollManager(scrollManager, oldScrollManager) {
    oldScrollManager == null ? void 0 : oldScrollManager.destroy();
    if (scrollManager) {
      return ScrollManager.new({
        element: this.element,
        owner: this
      }, scrollManager);
    } else {
      return null;
    }
  }
  /**
   * Cleanup
   * @private
   */
  doDestroy() {
    var _a5, _b2;
    const me = this;
    me.detachListeners(storeListenerName2);
    (_a5 = me.scrollManager) == null ? void 0 : _a5.destroy();
    for (const feature of Object.values(me.features)) {
      (_b2 = feature.destroy) == null ? void 0 : _b2.call(feature);
    }
    me._focusedCell = null;
    me.columns.destroy();
    super.doDestroy();
  }
  /**
   * Adds extra classes to the Grid element after it's been configured.
   * Also iterates through features, thus ensuring they have been initialized.
   * @private
   */
  setGridClassList(classList) {
    const me = this;
    Object.values(me.features).forEach((feature) => {
      if (feature.disabled || feature === false) {
        return;
      }
      let featureClass;
      if (Object.prototype.hasOwnProperty.call(feature.constructor, "featureClass")) {
        featureClass = feature.constructor.featureClass;
      } else {
        featureClass = `b-${feature instanceof Base ? feature.$$name : feature.constructor.name}`;
      }
      if (featureClass) {
        classList.add(featureClass.toLowerCase());
      }
    });
  }
  //endregion
  // region Feature events
  // For documentation & typings purposes
  /**
   * Fires after a sub grid is collapsed.
   * @event subGridCollapse
   * @param {Grid.view.Grid} source The firing Grid instance
   * @param {Grid.view.SubGrid} subGrid The sub grid instance
   */
  /**
   * Fires after a sub grid is expanded.
   * @event subGridExpand
   * @param {Grid.view.Grid} source The firing Grid instance
   * @param {Grid.view.SubGrid} subGrid The sub grid instance
   */
  /**
   * Fires before a row is rendered.
   * @event beforeRenderRow
   * @param {Grid.view.Grid} source The firing Grid instance.
   * @param {Grid.row.Row} row The row about to be rendered.
   * @param {Core.data.Model} record The record for the row.
   * @param {Number} recordIndex The zero-based index of the record.
   */
  /**
   * Fires after a row is rendered.
   * @event renderRow
   * @param {Grid.view.Grid} source The firing Grid instance.
   * @param {Grid.row.Row} row The row that has been rendered.
   * @param {Core.data.Model} record The record for the row.
   * @param {Number} recordIndex The zero-based index of the record.
   */
  //endregion
  //region Grid template & elements
  compose() {
    const { autoHeight, enableSticky, enableTextSelection, fillLastColumn, positionMode, showDirty } = this;
    return {
      class: {
        [`b-grid-${positionMode}`]: 1,
        "b-enable-sticky": enableSticky,
        "b-grid-notextselection": !enableTextSelection,
        "b-autoheight": autoHeight,
        "b-fill-last-column": fillLastColumn,
        "b-show-dirty": showDirty,
        "b-show-dirty-during-edit": showDirty == null ? void 0 : showDirty.duringEdit
      }
    };
  }
  async recomposeInternal() {
    var _a5;
    let scrollState;
    const { isConstructing } = this;
    if (!isConstructing) {
      scrollState = this.storeScroll();
    }
    await super.recomposeInternal();
    if (!isConstructing) {
      const oldValues = [...Object.keys(scrollState.scrollLeft).map((k) => scrollState.scrollLeft[k]), scrollState.scrollTop];
      if (Math.max(...oldValues)) {
        const newState = this.storeScroll();
        if (!Math.max([...Object.keys(newState.scrollLeft).map((k) => newState.scrollLeft[k]), newState.scrollTop])) {
          (_a5 = this.restoreScroll) == null ? void 0 : _a5.call(this, scrollState);
        }
      }
    }
  }
  get cellCls() {
    const { _cellCls } = this;
    return _cellCls.value || _cellCls;
  }
  get bodyConfig() {
    const { autoHeight, hideFooters, hideHeaders } = this;
    return {
      reference: "bodyElement",
      className: {
        "b-autoheight": autoHeight,
        "b-grid-panel-body": 1
      },
      // Only include aria-labelled-by if we have a header
      [this.hasHeader ? "aria-labelledby" : ""]: `${this.id}-panel-title`,
      children: {
        headerContainer: {
          "aria-hidden": hideHeaders,
          role: hideHeaders ? null : "row",
          "aria-rowindex": 1,
          className: {
            "b-grid-header-container": 1,
            "b-hidden": hideHeaders
          }
        },
        bodyContainer: {
          className: "b-grid-body-container",
          role: "presentation",
          children: {
            verticalScroller: {
              role: "presentation",
              className: "b-grid-vertical-scroller"
            }
          }
        },
        virtualScrollers: {
          className: "b-virtual-scrollers b-hide-display",
          role: null
        },
        footerContainer: {
          "aria-hidden": hideFooters,
          role: hideFooters ? null : "row",
          className: {
            "b-grid-footer-container": 1,
            "b-hidden": hideFooters
          }
        }
      }
    };
  }
  get contentElement() {
    return this.verticalScroller;
  }
  get overflowElement() {
    return this.bodyContainer;
  }
  updateCellEllipsis(value2) {
    this.element.classList.toggle("b-use-ellipsis", value2);
  }
  updateHideHeaders(hide) {
    var _a5;
    hide = Boolean(hide);
    (_a5 = this.headerContainer) == null ? void 0 : _a5.classList.toggle("b-hidden", hide);
    this.eachSubGrid((subGrid) => subGrid.toggleHeaders(hide));
  }
  updateHideFooters(hide) {
    var _a5;
    hide = Boolean(hide);
    (_a5 = this.footerContainer) == null ? void 0 : _a5.classList.toggle("b-hidden", hide);
    this.eachSubGrid((subGrid) => {
      subGrid.scrollable[hide ? "removePartner" : "addPartner"](subGrid.footer.scrollable, "x");
    });
  }
  updateHideHorizontalScrollbar(hide) {
    hide = Boolean(hide);
    this.eachSubGrid((subGrid) => {
      subGrid.virtualScrollerElement.classList.toggle("b-hide-display", hide);
      if (!this.isConfiguring) {
        subGrid.scrollable[hide ? "removePartner" : "addPartner"](subGrid.fakeScroller, "x");
        if (!hide) {
          subGrid.refreshFakeScroll();
        }
      }
    });
  }
  //endregion
  //region Columns
  changeColumns(columns, currentStore) {
    const me = this;
    if (!columns && currentStore) {
      if (me.isDestroying) {
        currentStore.owner === me && currentStore.destroy();
      } else {
        currentStore.removeAll();
      }
      return currentStore;
    }
    if (columns.isStore) {
      (currentStore == null ? void 0 : currentStore.owner) === me && currentStore.destroy();
      columns.grid = me;
      return columns;
    }
    if (Array.isArray(columns)) {
      if (currentStore) {
        const columnsBefore = currentStore.allRecords.slice();
        currentStore.data = columns;
        for (const oldColumn of columnsBefore) {
          if (!currentStore.includes(oldColumn)) {
            oldColumn.destroy();
          }
        }
        return currentStore;
      }
      columns = { data: columns };
    }
    if (currentStore) {
      throw new Error("Replacing ColumnStore is not supported");
    }
    return ColumnStore.new({
      grid: me,
      owner: me
    }, columns);
  }
  updateColumns(columns, was) {
    var _a5, _b2;
    const me = this, { formulaProviders } = me;
    (_a5 = super.updateColumns) == null ? void 0 : _a5.call(this, columns, was);
    columns.ion({
      refresh: me.onColumnsChanged,
      sort: me.onColumnsChanged,
      change: me.onColumnsChanged,
      move: me.onColumnsChanged,
      thisObj: me
    });
    columns.ion(columnResizeEvent(me.onColumnsResized, me));
    if (BrowserHelper.isTouchDevice) {
      me.touch = true;
      columns.forEach((column) => {
        const { touchConfig } = column;
        if (touchConfig) {
          column.applyState(touchConfig);
        }
      });
    }
    (_b2 = me.bodyElement) == null ? void 0 : _b2.setAttribute("aria-colcount", columns.visibleColumns.length);
    if (formulaProviders) {
      const { modelClass } = me.store;
      columns.forEach((col) => {
        if (col.data.formula && col.field && modelClass.fieldMap[col.field]) {
          modelClass.fieldMap[col.field].formulaProviders = formulaProviders;
        }
      });
    }
  }
  onColumnsChanged({ type, action, changes, record: column, records: changedColumns, isMove }) {
    var _a5;
    const me = this, {
      columns,
      checkboxSelectionColumn
    } = me, changedFields = changes && Object.keys(changes), isSingleFieldChange = (changedFields == null ? void 0 : changedFields.length) === 1;
    isMove = isMove === true ? true : isMove && Object.values(isMove).some((field) => field);
    me.beforeColumnsChange({ type, action, changes, column, columns: changedColumns });
    if (isMove || type === "refresh" && action !== "batch" && action !== "sort" || // Ignore the update of parentIndex following a column move (we redraw on the insert)
    action === "update" && isSingleFieldChange && "parentIndex" in changes || // Ignore sort caused by sync, will refresh on the batch instead
    action === "sort" && columns.isSyncingDataOnLoad) {
      return;
    }
    const addingColumnToNonExistingSubGrid = action === "add" && changedColumns.some((col) => col.region && !me.subGrids[col.region]);
    if (me.isConfiguring || !addingColumnToNonExistingSubGrid && (!me.isPainted || isMove && action === "remove")) {
      return;
    }
    if (action === "add") {
      for (const column2 of changedColumns) {
        const { region } = column2;
        if (!me.subGrids[region]) {
          me.add(me.createSubGrid(region, (_a5 = me.subGridConfigs) == null ? void 0 : _a5[region]));
          me.subGrids[region].initScroll();
        }
      }
    } else if (action === "update") {
      const changedField = changedFields[0];
      if (("width" in changes || "minWidth" in changes || "maxWidth" in changes || "flex" in changes) && !("region" in changes)) {
        const { region } = column;
        columns.visibleColumns.forEach((col) => {
          if (col.region === region && col.repaintOnResize) {
            me.refreshColumn(col);
          }
        });
        me.afterColumnsChange({ type, action, changes, column });
        return;
      }
      if ("text" in changes && isSingleFieldChange) {
        column.subGrid.refreshHeader();
        return;
      }
      if ("hidden" in changes) {
        const subGrid = me.getSubGridFromColumn(column.id);
        subGrid.header.fixHeaderWidths();
        subGrid.footer.fixFooterWidths();
        subGrid.updateHasFlex();
      } else if (isSingleFieldChange && !(changedField in fullRefreshAfterUpdateFields) && !column.hidden && !column.subGrid.collapsed) {
        column.subGrid.refreshHeader();
        me.refreshColumn(column);
        return;
      }
    } else if (action === "remove") {
      changedColumns.forEach((c) => {
        var _a6;
        return (_a6 = c.freeColumnWidgets) == null ? void 0 : _a6.call(c);
      });
    }
    if (action === "dataset" || action === "batch" || action === "update" && "region" in changes) {
      const regions = columns.getDistinctValues("region", true), { toRemove, toAdd } = ArrayHelper.delta(regions, me.regions, true);
      me.remove(toRemove.map((region) => me.getSubGrid(region)));
      me.add(toAdd.map((region) => me.createSubGrid(region, me.subGridConfigs[region])));
    }
    if (checkboxSelectionColumn && !columns.includes(checkboxSelectionColumn)) {
      const insertIndex = columns.indexOf(columns.findRecord("type", "rownumber")) + 1;
      columns.insert(insertIndex, checkboxSelectionColumn, true);
    }
    if (!me._suspendRenderContentsOnColumnsChanged) {
      me.renderContents();
    }
    me.syncFlexedSubCols();
    me.bodyElement.setAttribute("aria-colcount", columns.visibleColumns.length);
    me.afterColumnsChange({ type, action, changes, column, columns: changedColumns });
  }
  onColumnsResized({ changes, record: column }) {
    var _a5;
    const me = this;
    if (me.isConfiguring) {
      return;
    }
    const domWidth = DomHelper.setLength(column.width), domMinWidth = DomHelper.setLength(column.minWidth), domMaxWidth = DomHelper.setLength(column.maxWidth), subGrid = me.getSubGridFromColumn(column.id);
    subGrid.header.fixHeaderWidths();
    subGrid.footer.fixFooterWidths();
    subGrid.updateHasFlex();
    if (!(column.flex && column.childLevel)) {
      if (!me.cellEls || column !== me.lastColumnResized) {
        me.cellEls = DomHelper.children(
          me.element,
          `.b-grid-cell[data-column-id="${column.id}"]`
        );
        me.lastColumnResized = column;
      }
      for (const cell of me.cellEls) {
        if ("width" in changes) {
          cell.style.width = domWidth;
        }
        if ("minWidth" in changes) {
          cell.style.minWidth = domMinWidth;
        }
        if ("maxWidth" in changes) {
          cell.style.maxWidth = domMaxWidth;
        }
        if ("flex" in changes) {
          cell.style.flex = (_a5 = column.flex) != null ? _a5 : null;
        }
      }
    }
    if (!me.resizingColumns) {
      me.afterColumnsResized(column);
    }
    me.syncFlexedSubCols();
  }
  afterColumnsResized(column) {
    const me = this;
    me.eachSubGrid((subGrid) => {
      if (!subGrid.collapsed && (!column || column.region === subGrid.region)) {
        subGrid.fixWidths();
        subGrid.fixRowWidthsInSafariEdge();
      }
    });
    me.lastColumnResized = me.cellEls = null;
    me.bufferedAfterColumnsResized(column);
    me.onHeightChange();
  }
  syncFlexedSubCols() {
    const flexedSubCols = this.columns.query((c) => c.flex && c.childLevel && c.element);
    if (flexedSubCols) {
      for (const column of flexedSubCols) {
        const width = column.element.getBoundingClientRect().width, cellEls = DomHelper.children(
          this.element,
          `.b-grid-cell[data-column-id="${column.id}"]`
        );
        for (const cell of cellEls) {
          cell.style.flex = `0 0 ${width}px`;
        }
      }
    }
  }
  bufferedAfterColumnsResized(column) {
    if (this.columns.usesAutoHeight) {
      this.refreshRows();
    }
    this.refreshVirtualScrollbars();
    this.eachSubGrid((subGrid) => {
      if (!subGrid.collapsed && (!column || column.region === subGrid.region)) {
        subGrid.refreshFakeScroll();
      }
    });
  }
  bufferedElementResize() {
    this.refreshRows();
  }
  onInternalResize(element, newWidth, newHeight, oldWidth, oldHeight) {
    if (DomHelper.scrollBarWidth && newWidth < oldWidth) {
      this.eachSubGrid((subGrid) => {
        if (subGrid.flex) {
          subGrid.onElementResize(subGrid.element);
        }
      });
    }
    super.onInternalResize(...arguments);
    if (this.isPainted && newWidth !== oldWidth && this.columns.usesFlexAutoHeight) {
      this.bufferedElementResize();
    }
  }
  //endregion
  //region Rows
  /**
   * Get the topmost visible grid row
   * @member {Grid.row.Row} firstVisibleRow
   * @readonly
   * @category Rows
   */
  /**
   * Get the last visible grid row
   * @member {Grid.row.Row} lastVisibleRow
   * @readonly
   * @category Rows
   */
  /**
   * Get the Row that is currently displayed at top.
   * @member {Grid.row.Row} topRow
   * @readonly
   * @category Rows
   * @private
   */
  /**
   * Get the Row currently displayed furthest down.
   * @member {Grid.row.Row} bottomRow
   * @readonly
   * @category Rows
   * @private
   */
  /**
   * Get Row for specified record id.
   * @function getRowById
   * @param {Core.data.Model|String|Number} recordOrId Record id (or a record)
   * @returns {Grid.row.Row} Found Row or null if record not rendered
   * @category Rows
   * @private
   */
  /**
   * Returns top and bottom for rendered row or estimated coordinates for unrendered.
   * @function getRecordCoords
   * @param {Core.data.Model|String|Number} recordOrId Record or record id
   * @returns {Object} Record bounds with format { top, height, bottom }
   * @category Calculations
   * @private
   */
  /**
   * Get the Row at specified index. "Wraps" index if larger than available rows.
   * @function getRow
   * @param {Number} index
   * @returns {Grid.row.Row}
   * @category Rows
   * @private
   */
  /**
   * Get a Row for either a record, a record id or an HTMLElement
   * @function getRowFor
   * @param {HTMLElement|Core.data.Model|String|Number} recordOrId Record or record id or HTMLElement
   * @returns {Grid.row.Row} Found Row or `null` if record not rendered
   * @category Rows
   */
  /**
   * Get a Row from an HTMLElement
   * @function getRowFromElement
   * @param {HTMLElement} element
   * @returns {Grid.row.Row} Found Row or `null` if record not rendered
   * @category Rows
   * @private
   */
  changeRowManager(rowManager, oldRowManager) {
    const me = this;
    if (!me._isRowMeasured) {
      me.measureRowHeight();
    }
    oldRowManager == null ? void 0 : oldRowManager.destroy();
    if (rowManager) {
      const result = RowManager.new({
        grid: me,
        rowHeight: me.rowHeight,
        rowScrollMode: me.rowScrollMode || "move",
        autoHeight: me.autoHeight,
        fixedRowHeight: me.fixedRowHeight,
        internalListeners: {
          changeTotalHeight: "onRowManagerChangeTotalHeight",
          requestScrollChange: "onRowManagerRequestScrollChange",
          thisObj: me
        }
      }, rowManager);
      me.relayEvents(result, ["beforeRenderRow", "renderRow", "removeRows"]);
      me._rowManager = null;
      return result;
    }
  }
  // Manual relay needed for Split feature to catch the config change
  updateRowHeight(rowHeight) {
    if (!this.isConfiguring && this.rowManager) {
      this.rowManager.rowHeight = rowHeight;
    }
    this.element.style.setProperty("--b-grid-row-height", DomHelper.setLength(rowHeight));
  }
  updateFixedRowHeight(fixedRowHeight) {
    var _a5, _b2;
    if (fixedRowHeight && ((_b2 = (_a5 = this.columns || this.peekConfig("columns")) == null ? void 0 : _a5.some) == null ? void 0 : _b2.call(_a5, (col) => col.autoHeight))) {
      console.warn("One or more columns have `autoHeight` set, which is incompatible with `fixedRowHeight`.");
    }
  }
  // Default implementation, documented in `defaultConfig`
  getRowHeight(record) {
    return record.rowHeight;
  }
  // Hook for features that need to alter the row height
  processRowHeight(record, height) {
  }
  //endregion
  //region Store
  getAsyncEventSuffixForStore(store) {
    return this.asyncEventSuffix;
  }
  /**
   * Hooks up data store listeners
   * @private
   * @category Store
   */
  bindStore(store) {
    const suffix = this.getAsyncEventSuffixForStore(store), { masterStore } = store;
    store.ion({
      name: storeListenerName2,
      [`refresh${suffix}`]: "onStoreDataChange",
      [`add${suffix}`]: "onStoreAdd",
      [`remove${suffix}`]: "onStoreRemove",
      [`replace${suffix}`]: "onStoreReplace",
      [`removeAll${suffix}`]: "onStoreRemoveAll",
      [`move${suffix}`]: store.tree ? null : "onFlatStoreMove",
      change: "relayStoreDataChange",
      idChange: "onStoreRecordIdChange",
      update: "onStoreUpdateRecord",
      beforeRequest: "onStoreBeforeRequest",
      afterRequest: "onStoreAfterRequest",
      exception: "onStoreException",
      commit: "onStoreCommit",
      clearChanges: "onStoreRecordClearChanges",
      startApplyChangeset: "internalOnStoreStartApplyChangeset",
      endApplyChangeset: "internalOnStoreEndApplyChangeset",
      lazyLoadStarted: "onStoreBeforeRequest",
      lazyLoadEnded: "onStoreAfterRequest",
      thisObj: this
    });
    masterStore == null ? void 0 : masterStore.ion({
      name: storeListenerName2,
      // Using same name as ^ to unbind at same time
      beforeRequest: "onStoreBeforeRequest",
      afterRequest: "onStoreAfterRequest",
      lazyLoadEnded: "onStoreAfterRequest",
      thisObj: this
    });
    super.bindStore(store);
  }
  unbindStore(oldStore) {
    this.detachListeners(storeListenerName2);
    if (this.destroyStore) {
      oldStore.destroy();
    }
  }
  get $store() {
    return this.originalStore || this.store;
  }
  changeStore(store) {
    var _a5;
    if (store == null) {
      return null;
    }
    if (typeof store === "string") {
      store = Store.getStore(store);
    }
    if (!store.isStore) {
      store = ObjectHelper.assign({
        data: this.data,
        tree: Boolean((_a5 = this.initialConfig.features) == null ? void 0 : _a5.tree)
      }, store);
      if (!store.data) {
        delete store.data;
      }
      if (!store.modelClass) {
        store.modelClass = GridRowModel;
      }
      store = new (store.readUrl ? AjaxStore : Store)(store);
    }
    return store;
  }
  updateStore(store, was) {
    var _a5, _b2;
    const me = this;
    (_a5 = super.updateStore) == null ? void 0 : _a5.call(this, store, was);
    if (was) {
      me.unbindStore(was);
    }
    if (store) {
      if (was) {
        me.deselectAll();
      }
      me.onStoreLoadOverride();
      me.bindStore(store);
    }
    me.trigger("bindStore", { store, oldStore: was });
    if (!me.isDestroying && me.isPainted && !me.refreshSuspended) {
      (_b2 = me._rowManager) == null ? void 0 : _b2.reinitialize();
    }
  }
  /**
   * Rerenders a cell if a record is updated in the store
   * @private
   * @category Store
   */
  onStoreUpdateRecord({ source: store, record, changes }) {
    const me = this;
    if (me.refreshSuspended) {
      return;
    }
    if (me.forceFullRefresh) {
      me.rowManager.refresh();
      me.forceFullRefresh = false;
    } else {
      let row;
      if (record.isFieldModified("id")) {
        row = me.getRowFor(record.meta.modified.id);
      }
      row = row || me.getRowFor(record);
      if (!row) {
        return;
      }
      if (me.fullRowRefresh || record.isSpecialRow) {
        const index = store.indexOf(record);
        if (index !== -1) {
          row.render(index, record);
        }
      } else {
        me.columns.visibleColumns.forEach((column) => {
          const field = column.field, isSafe = column.constructor.simpleRenderer && !Object.prototype.hasOwnProperty.call(column.data, "renderer");
          if (!isSafe || changes[field]) {
            const cellElement = row.getCell(field);
            if (cellElement) {
              row.renderCell(cellElement);
            }
          }
        });
      }
    }
  }
  refreshFromRowOnStoreAdd(row, context) {
    const me = this, { rowManager } = me;
    rowManager.renderFromRow(row);
    rowManager.trigger("changeTotalHeight", { totalHeight: rowManager.totalHeight });
    if (me.store.count === 1) {
      me.callEachSubGrid("refreshFakeScroll");
    }
  }
  onMaskAutoClose(mask) {
    super.onMaskAutoClose(mask);
    this.toggleEmptyText();
  }
  /**
   * Refreshes rows when data is added to the store
   * @private
   * @category Store
   */
  onStoreAdd({
    source: store,
    records,
    index,
    oldIndex,
    isChild,
    oldParent,
    parent,
    isMove,
    isExpand,
    isExpandAll
  }) {
    const me = this, { rowManager } = me;
    if (!me.isPainted || isExpandAll || me.refreshSuspended) {
      return;
    }
    if (isExpand && me.animateTreeNodeToggle) {
      return rowManager.insert(rowManager.getRowFor(parent).index + 1, records.length);
    }
    const hasExpandedOldParent = isMove && records.some((record) => {
      if (isMove[record.id]) {
        const oldParent2 = store.getById(record.meta.modified.parentId);
        return (oldParent2 == null ? void 0 : oldParent2.isExpanded(store)) && (oldParent2 == null ? void 0 : oldParent2.ancestorsExpanded(store));
      }
    });
    if (isChild && !records[0].ancestorsExpanded(store) && !hasExpandedOldParent) {
      if (!parent.isLeaf) {
        const parentRow = rowManager.getRowById(parent);
        if (parentRow) {
          rowManager.renderRows([parentRow]);
        }
      }
      return;
    }
    rowManager.calculateRowCount(false, true, true);
    if (store.isFiltered) {
      index = store.indexOf(records[0]);
    }
    const {
      topIndex,
      rows,
      rowCount
    } = rowManager, bottomIndex = rowManager.topIndex + rowManager.rowCount - 1, dataStart = index, dataEnd = index + records.length - 1, atEnd = bottomIndex >= store.count - records.length - 1;
    if (oldParent || oldIndex > -1 || isChild && isMove && Object.values(isMove).some((v) => v)) {
      rowManager.refresh();
    } else if (dataStart >= topIndex && dataStart < topIndex + rowCount) {
      me.refreshFromRowOnStoreAdd(rows[dataStart - topIndex], ...arguments);
    } else if (dataEnd >= topIndex && dataEnd < topIndex + rowCount) {
      rowManager.refresh();
    } else {
      if (atEnd && index > bottomIndex) {
        rowManager.fillBelow(me._scrollTop || 0);
      } else if (index < topIndex) {
        rowManager.refresh();
      }
      rowManager.estimateTotalHeight(true);
    }
  }
  /**
   * Responds to exceptions signalled by the store
   * @private
   * @category Store
   */
  onStoreException({ action, type, response, exceptionType, error }) {
    var _a5;
    const me = this;
    let message;
    switch (type) {
      case "server":
        message = response.message || me.L("L{unspecifiedFailure}");
        break;
      case "exception":
        message = exceptionType === "network" ? me.L("L{networkFailure}") : (error == null ? void 0 : error.message) || ((_a5 = response == null ? void 0 : response.parsedJson) == null ? void 0 : _a5.message) || me.L("L{parseFailure}");
        break;
    }
    me.applyMaskError(
      `<div class="b-grid-load-failure">
                <div class="b-grid-load-fail">${me.L(action === "read" ? "L{loadFailedMessage}" : "L{syncFailedMessage}")}</div>
                ${(response == null ? void 0 : response.url) ? `<div class="b-grid-load-fail">${response.url}</div>` : ""}
                <div class="b-grid-load-fail">${me.L("L{serverResponse}")}</div>
                <div class="b-grid-load-fail">${message}</div>
            </div>`
    );
  }
  /**
   * Refreshes rows when data is changed in the store
   * @private
   * @category Store
   */
  onStoreDataChange({ action, changes, source: store, removed, added }) {
    var _a5;
    (_a5 = super.onStoreDataChange) == null ? void 0 : _a5.call(this, ...arguments);
    const me = this, { rowManager } = me, { rowCount } = rowManager, removedCount = (removed == null ? void 0 : removed.length) || 0;
    if (me.refreshSuspended || !rowManager) {
      return;
    }
    if (action === "filter" && removedCount && !(added == null ? void 0 : added.length) && rowCount && removedCount < rowCount && removed.every((record) => rowManager.getRowById(record.id))) {
      const indicesToRemove = removed.map((record) => rowManager.getRowFor(record).index).sort(ascending), { length } = indicesToRemove;
      if (length && indicesToRemove[length - 1] === indicesToRemove[0] + length - 1) {
        const { animateRemovingRows } = me;
        me.animateRemovingRows = !me.features.cellEdit.isEditing && me.animateFilterRemovals;
        const result = me.onStoreRemove({
          action,
          source: store,
          records: removed
        });
        me.animateRemovingRows = animateRemovingRows;
        return result;
      }
    }
    if (action === "dataset") {
      rowManager.clearKnownHeights();
      if (store.isTree && store.isFiltered) {
        return;
      }
    }
    const isGroupFieldChange = store.isGrouped && changes && store.groupers.some((grp) => grp.rootFieldName in changes);
    if (me.isPainted && !isGroupFieldChange) {
      me.renderRows(Boolean(!(action in datasetReplaceActions) || me.preserveScrollOnDatasetChange || store.isChained && store.isFillingFromMaster));
    }
    me.toggleEmptyText();
  }
  /**
   * The hook is called when the id of a record has changed.
   * @private
   * @category Store
   */
  onStoreRecordIdChange() {
    var _a5;
    (_a5 = super.onStoreRecordIdChange) == null ? void 0 : _a5.call(this, ...arguments);
  }
  /**
   * Shows a load mask while the connected store is loading
   * @private
   * @category Store
   */
  onStoreBeforeRequest(params) {
    if (!this.isLockedRows && (!this.store.count || !this.store.lazyLoad || (params == null ? void 0 : params.isReload))) {
      this.applyLoadMask();
    }
  }
  /**
   * Hides load mask after a load request ends either in success or failure
   * @private
   * @category Store
   */
  onStoreAfterRequest(event) {
    if (this.loadMask && !event.exception) {
      this.masked = null;
      this.toggleEmptyText();
    }
  }
  needsFullRefreshOnStoreRemove({ isCollapse }) {
    var _a5, _b2;
    const { store } = this;
    return ((_b2 = (_a5 = this.features) == null ? void 0 : _a5.group) == null ? void 0 : _b2.enabled) && store.isGrouped || // Need to redraw parents when children are removed since they might be converted to leaves
    store.tree && !isCollapse && store.modelClass.convertEmptyParentToLeaf;
  }
  /**
   * Animates removal of record.
   * @private
   * @category Store
   */
  onStoreRemove({ source: store, records, isCollapse, isChild, isMove, isCollapseAll }) {
    var _a5;
    if (!this.isPainted || isMove || isCollapseAll) {
      return;
    }
    (_a5 = super.onStoreRemove) == null ? void 0 : _a5.call(this, ...arguments);
    const me = this, { rowManager } = me;
    rowManager.invalidateKnownHeight(records);
    if (me.refreshSuspended) {
      return;
    }
    if (me.animateRemovingRows && !isCollapse && !isChild) {
      const rowsToRemove = records.flatMap((record) => rowManager.getRowFor(record) || []);
      if (rowsToRemove.length) {
        const [topRow] = rowsToRemove;
        me.executeAndAwaitAnimations(
          topRow._elementsArray[0],
          () => {
            me.isAnimating = true;
            rowsToRemove.forEach((row) => row.addCls("b-deleting-row"));
          },
          () => {
            me.isAnimating = false;
            rowsToRemove.forEach((row) => !row.isDestroyed && row.removeCls("b-deleting-row"));
            rowManager.refresh();
            me.trigger("rowRemove");
            me.afterRemove(arguments[0]);
          }
        );
      }
    } else if (isCollapse && me.animateTreeNodeToggle) {
      const indicesToRemove = records.flatMap((record) => {
        const row = rowManager.getRowFor(record);
        return row ? row.index : emptyArray11;
      }).sort(ascending), { length } = indicesToRemove;
      if (length && indicesToRemove[length - 1] === indicesToRemove[0] + length - 1) {
        return rowManager.insert(indicesToRemove[0], -indicesToRemove.length);
      }
    } else if (me.needsFullRefreshOnStoreRemove(...arguments)) {
      rowManager.refresh();
      me.afterRemove(arguments[0]);
    } else {
      const { rows } = rowManager, topRowIndex = records.reduce((result, record) => {
        const row = rowManager.getRowById(record.id);
        if (row) {
          result = Math.min(result, rows.indexOf(row));
        }
        return result;
      }, rows.length);
      if (rows[topRowIndex]) {
        !me.refreshSuspended && rowManager.renderFromRow(rows[topRowIndex]);
      } else {
        rowManager.trigger("changeTotalHeight", { totalHeight: rowManager.totalHeight });
      }
      me.trigger("rowRemove", { isCollapse });
      me.afterRemove(arguments[0]);
    }
  }
  onFlatStoreMove({ from, to }) {
    const { rowManager, store } = this, {
      topIndex,
      rowCount
    } = rowManager, [dataStart, dataEnd] = [from, to].sort((a, b) => a - b), visibleStart = store.indexOf(store.getAt(dataStart, true)), visibleEnd = store.indexOf(store.getAt(dataEnd, true));
    if (visibleStart >= topIndex && visibleStart < topIndex + rowCount) {
      rowManager.renderFromRow(rowManager.rows[visibleStart - topIndex]);
    } else if (visibleEnd >= topIndex && visibleEnd < topIndex + rowCount) {
      rowManager.refresh();
    }
  }
  onStoreReplace({ records, all }) {
    const { rowManager } = this;
    if (all) {
      rowManager.clearKnownHeights();
      rowManager.refresh();
    } else {
      const rows = records.reduce((rows2, [, record]) => {
        const row = this.getRowFor(record);
        if (row) {
          rows2.push(row);
        }
        return rows2;
      }, []);
      rowManager.invalidateKnownHeight(records);
      rowManager.renderRows(rows);
    }
  }
  relayStoreDataChange(event) {
    var _a5;
    (_a5 = this.ariaElement) == null ? void 0 : _a5.setAttribute("aria-rowcount", this.store.count + 1);
    if (!this.project) {
      return this.trigger("dataChange", { ...event, store: event.source, source: this });
    }
  }
  /**
   * Rerenders grid when all records have been removed
   * @private
   * @category Store
   */
  onStoreRemoveAll() {
    var _a5;
    (_a5 = super.onStoreRemoveAll) == null ? void 0 : _a5.call(this, ...arguments);
    if (this.isPainted) {
      this.rowManager.clearKnownHeights();
      this.renderRows(false);
      this.toggleEmptyText();
    }
  }
  // Refresh dirty cells on commit
  onStoreCommit({ changes }) {
    const { rowManager, showDirty } = this, rows = [], records = showDirty ? [
      ...changes.modified || [],
      ...showDirty.newRecord && changes.added || []
    ] : [];
    if (records.length) {
      records.forEach((record) => {
        const row = rowManager.getRowFor(record);
        row && rows.push(row);
      });
      rowManager.renderRows(rows);
    }
  }
  // Refresh dirty cells after a call to Model#clearChanges
  onStoreRecordClearChanges({ record }) {
    const row = this.rowManager.getRowFor(record);
    if (row) {
      this.rowManager.renderRows([row]);
    }
  }
  internalOnStoreStartApplyChangeset() {
    this.suspendRefresh();
    if (this.constructor.bindStoreChangeset) {
      this.captureScrollTargetRow();
    }
  }
  internalOnStoreEndApplyChangeset() {
    this.resumeRefresh(true);
    if (this.constructor.bindStoreChangeset) {
      this.restoreScrollTargetRow();
    }
  }
  /**
   * Remember scroll position when store is about to apply a changeset
   * @private
   */
  captureScrollTargetRow() {
    const me = this;
    if (me.preserveScroll) {
      const { firstFullyVisibleRow: firstRow, lastVisibleRow: lastRow } = me.rowManager;
      if (firstRow) {
        me.lastVisibleRowIds = [firstRow.id];
        for (let index = firstRow.dataIndex + 1; index <= lastRow.dataIndex; index++) {
          me.lastVisibleRowIds.push(me.rowManager.getRow(index).id);
        }
        me.lastTopRowOffset = me.scrollable.getDeltaTo(
          firstRow.element,
          { block: "start", x: false, constrainToScrollable: false }
        );
      }
    }
  }
  /**
   * Restore scroll position. Go to the topmost row formerly in the view that is still present in the dataset.
   * @private
   */
  restoreScrollTargetRow() {
    const me = this;
    let didOverscroll = false;
    if (me.preserveScroll) {
      if (me.lastVisibleRowIds) {
        me.rowManager.refresh();
        if (me.scrollable.scrollHeight >= me.lastOverscrollHeight && me.scrollable.y === me.lastOverscrollY) {
          me.rowManager.fillBelow(me.scrollable.y);
        } else {
          me.lastOverscrollHeight = null;
          me.lastOverscrollY = null;
        }
        const targetId = me.lastVisibleRowIds.find((rowId) => me.store.getById(rowId));
        if (targetId != void 0 && targetId !== me.rowManager.firstFullyVisibleRow.id) {
          if (me.preserveScroll.overscroll) {
            const scrollNeeded = me.scrollable.getDeltaTo(
              me.getRecordCoords(targetId),
              {
                block: "start",
                x: false,
                constrainToScrollable: false,
                edgeOffset: me.lastTopRowOffset.yDelta
              }
            ).yDelta;
            if (scrollNeeded > me.scrollable.maxY - me.scrollable.y) {
              me.scrollable.scrollHeight += scrollNeeded;
              didOverscroll = true;
            }
          }
          me.scrollRowIntoView(targetId, {
            block: "start",
            edgeOffset: me.lastTopRowOffset.yDelta,
            x: false
          });
        }
      }
      me.lastVisibleRowIds = void 0;
      me.lastTopRowOffset = void 0;
      if (didOverscroll) {
        me.lastOverscrollHeight = me.scrollable.scrollHeight;
        me.lastOverscrollY = me.scrollable.y;
      }
    }
  }
  // Documented with config
  get data() {
    if (this._store) {
      return this._store.records;
    } else {
      return this._data;
    }
  }
  set data(data) {
    if (this._store) {
      this._store.data = data;
    } else {
      this._data = data;
    }
  }
  //endregion
  //region Context menu items
  /**
   * Populates the header context menu. Chained in features to add menu items.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Object<String,MenuItemConfig|Boolean|null>} options.items A named object to describe menu items
   * @internal
   */
  populateHeaderMenu({ column, items: items2 }) {
    const me = this, { subGrids, regions } = me, { parent } = column;
    let first = true;
    Object.entries(subGrids).forEach(([region, subGrid]) => {
      if (subGrid.sealedColumns) {
        return;
      }
      if (column.draggable && region !== column.region && (!parent && subGrids[column.region].columns.count > 1 || parent && parent.children.length > 1)) {
        const preceding = subGrid.element.compareDocumentPosition(subGrids[column.region].element) === document.DOCUMENT_POSITION_PRECEDING, moveRight = me.rtl ? !preceding : preceding, text = regions.length > 2 ? me.L("L{moveColumnTo}", me.optionalL(region)) : me.L(moveRight ? "L{moveColumnRight}" : "L{moveColumnLeft}");
        items2[`${region}Region`] = {
          targetSubGrid: region,
          text,
          icon: "b-fw-icon b-icon-column-move-" + (moveRight ? "right" : "left"),
          separator: first,
          disabled: !column.allowDrag,
          onItem: ({ item }) => {
            const lastRegionIndex = me.columns.indexOf(subGrids[item.targetSubGrid].columns.last);
            column.traverse((col) => col.region = region);
            me.columns.insert(lastRegionIndex + 1, column);
            me.scrollColumnIntoView(column);
          }
        };
        first = false;
      }
    });
  }
  /**
   * Populates the cell context menu. Chained in features to add menu items.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Core.data.Model} options.record Record for which the menu will be shown
   * @param {Object<String,MenuItemConfig|Boolean|null>} options.items A named object to describe menu items
   * @internal
   */
  populateCellMenu({ record, items: items2 }) {
  }
  getColumnDragToolbarItems(column, items2) {
    return items2;
  }
  //endregion
  //region Getters
  normalizeCellContext(cellContext) {
    const grid = this, { columns } = grid;
    if (cellContext.isLocation) {
      return cellContext;
    }
    if (cellContext.isModel) {
      return new GridLocation({
        grid,
        id: cellContext.id,
        columnId: columns.visibleColumns[0].id
      });
    }
    return new GridLocation(ObjectHelper.assign({ grid }, cellContext));
  }
  /**
   * Returns a cell if rendered or null if not found.
   * @param {GridLocationConfig} cellContext A cell location descriptor
   * @returns {HTMLElement|null}
   * @category Getters
   */
  getCell(cellContext) {
    const { store, columns } = this, { visibleColumns } = this.columns, rowIndex = !isNaN(cellContext.row) ? cellContext.row : !isNaN(cellContext.rowIndex) ? cellContext.rowIndex : store.indexOf(cellContext.record || cellContext.id), columnIndex = !isNaN(cellContext.column) ? cellContext.column : !isNaN(cellContext.columnIndex) ? cellContext.columnIndex : visibleColumns.indexOf(cellContext.column || columns.getById(cellContext.columnId) || columns.get(cellContext.field) || visibleColumns[0]);
    return rowIndex > -1 && rowIndex < store.count && columnIndex > -1 && columnIndex < visibleColumns.length && this.normalizeCellContext(cellContext).cell || null;
  }
  /**
   * Returns the header element for the column
   * @param {String|Number|Grid.column.Column} columnId or Column instance
   * @returns {HTMLElement} Header element
   * @category Getters
   */
  getHeaderElement(columnId) {
    if (columnId.isModel) {
      columnId = columnId.id;
    }
    return this.fromCache(`.b-grid-header[data-column-id="${columnId}"]`);
  }
  /**
   * Returns the footer element for the column (only relevant when using the Summary feature)
   * @param {String|Number|Grid.column.Column} columnId or Column instance
   * @returns {HTMLElement} Header element
   * @category Getters
   */
  getFooterElement(columnId) {
    if (columnId.isModel) {
      columnId = columnId.id;
    }
    return this.fromCache(`.b-grid-footer[data-column-id="${columnId}"]`);
  }
  getHeaderElementByField(field) {
    const column = this.columns.get(field);
    return column ? this.getHeaderElement(column) : null;
  }
  /**
   * Body height
   * @member {Number}
   * @readonly
   * @category Layout
   */
  get bodyHeight() {
    return this._bodyHeight;
  }
  /**
   * Header height
   * @member {Number}
   * @readonly
   * @category Layout
   */
  get headerHeight() {
    const me = this;
    if (me.isPainted && !me._headerHeight) {
      me._headerHeight = me.headerContainer.offsetHeight;
    }
    return me._headerHeight;
  }
  /**
   * Footer height
   * @member {Number}
   * @readonly
   * @category Layout
   */
  get footerHeight() {
    const me = this;
    if (me.isPainted && !me._footerHeight) {
      me._footerHeight = me.footerContainer.offsetHeight;
    }
    return me._footerHeight;
  }
  get isTreeGrouped() {
    var _a5;
    return Boolean((_a5 = this.features.treeGroup) == null ? void 0 : _a5.isGrouped);
  }
  /**
   * Searches up from the specified element for a grid row and returns the record associated with that row.
   * @param {HTMLElement} element Element somewhere within a row or the row container element
   * @returns {Core.data.Model} Record for the row
   * @category Getters
   */
  getRecordFromElement(element) {
    const el = element.closest(".b-grid-row");
    if (!el) {
      return null;
    }
    return this.$store.getById(el.dataset.id);
  }
  /**
   * Searches up from specified element for a grid cell or a header and returns the column which the cell belongs to
   * @param {HTMLElement} element Element somewhere in a cell
   * @returns {Grid.column.Column} Column to which the cell belongs
   * @category Getters
   */
  getColumnFromElement(element) {
    const cell = element.closest(".b-grid-cell, .b-grid-header");
    if (!cell)
      return null;
    if (cell.matches(".b-grid-header")) {
      return this.columns.getById(cell.dataset.columnId);
    }
    const cellData = DomDataStore.get(cell);
    return this.columns.getById(cellData.columnId);
  }
  // Only added for type checking, since it seems common to get it wrong in react/angular
  updateAutoHeight(autoHeight) {
    ObjectHelper.assertBoolean(autoHeight, "autoHeight");
  }
  updateColumnLines(columnLines) {
    ObjectHelper.assertBoolean(columnLines, "columnLines");
    DomHelper.toggleClasses(this.element, "b-no-column-lines", !columnLines);
  }
  updateRowLines(rowLines) {
    DomHelper.toggleClasses(this.element, "b-no-row-lines", !rowLines);
  }
  get keyMapElement() {
    return this.bodyElement;
  }
  //endregion
  //region Fix width & height
  /**
   * Sets widths and heights for headers, rows and other parts of the grid as needed
   * @private
   * @category Width & height
   */
  fixSizes() {
    this.callEachSubGrid("fixWidths");
    const colHeaders = this.headerContainer.querySelectorAll(".b-grid-header.b-depth-0");
    for (let i = 0, { length } = colHeaders; i < length; i++) {
      colHeaders[i].setAttribute("aria-colindex", i + 1);
    }
  }
  onRowManagerChangeTotalHeight({ totalHeight, immediate }) {
    return this.refreshTotalHeight(totalHeight, immediate);
  }
  /**
   * Makes height of vertical scroller match estimated total height of grid. Called when scrolling vertically and
   * when showing/hiding rows.
   * @param {Number} [height] Total height supplied by RowManager
   * @param {Boolean} [immediate] Flag indicating if buffered element sizing should be bypassed
   * @private
   * @category Width & height
   */
  refreshTotalHeight(height = this.rowManager.totalHeight, immediate = false) {
    const me = this;
    if (me.renderingRows || !me.isVisible) {
      return false;
    }
    const scroller = me.scrollable, delta = Math.abs(me.virtualScrollHeight - height), clientHeight = me._bodyRectangle.height, newMaxY = height - clientHeight;
    if (delta) {
      const isCritical = newMaxY - me._scrollTop < clientHeight * 2 || // Or if we have scrolled pass visual height
      me._verticalScrollHeight && me._verticalScrollHeight - clientHeight < me._scrollTop;
      scroller.scrollHeight = me.virtualScrollHeight = height;
      if (me.isPainted && (me.scrolling && !isCritical || delta < 100) && !immediate) {
        me.bufferedFixElementHeights();
      } else {
        me.virtualScrollHeightDirty && me.virtualScrollHeightDirty();
        me.bufferedFixElementHeights.cancel();
        me.fixElementHeights();
      }
    }
  }
  fixElementHeights() {
    const me = this, height = me.virtualScrollHeight, heightInPx = `${height}px`;
    me._verticalScrollHeight = height;
    me.verticalScroller.style.height = heightInPx;
    me.virtualScrollHeightDirty = false;
    if (me.autoHeight) {
      me._height = null;
      me.bodyContainer.style.height = heightInPx;
      me._bodyHeight = height;
      me.refreshBodyRectangle();
    }
    me.refreshVirtualScrollbars();
  }
  refreshBodyRectangle() {
    return this._bodyRectangle = Rectangle.client(this.bodyContainer);
  }
  //endregion
  //region Scroll & virtual rendering
  set scrolling(scrolling) {
    this._scrolling = scrolling;
  }
  get scrolling() {
    return this._scrolling;
  }
  /**
   * Activates automatic scrolling of a subGrid when mouse is moved closed to the edges. Useful when dragging DOM
   * nodes from outside this grid and dropping on the grid.
   * @param {Grid.view.SubGrid|String|Grid.view.SubGrid[]|String[]} subGrid A subGrid instance or its region name or
   * an array of either
   * @category Scrolling
   */
  enableScrollingCloseToEdges(subGrids) {
    this.scrollManager.startMonitoring({
      scrollables: [
        {
          element: this.scrollable.element,
          direction: "vertical"
        },
        ...ArrayHelper.asArray(subGrids || []).map((subGrid) => ({ element: (typeof subGrid === "string" ? this.subGrids[subGrid] : subGrid).scrollable.element }))
      ],
      direction: "horizontal"
    });
  }
  /**
   * Deactivates automatic scrolling of a subGrid when mouse is moved closed to the edges
   * @param {Grid.view.SubGrid|String|Grid.view.SubGrid[]|String[]} subGrid A subGrid instance or its region name or
   * an array of either
   * @category Scrolling
   */
  disableScrollingCloseToEdges(subGrids) {
    this.scrollManager.stopMonitoring([
      this.scrollable.element,
      ...ArrayHelper.asArray(subGrids || []).map((subGrid) => (typeof subGrid === "string" ? this.subGrids[subGrid] : subGrid).element)
    ]);
  }
  /**
   * Responds to request from RowManager to adjust scroll position. Happens when jumping to a scroll position with
   * variable row height.
   * @param {Number} bottomMostRowY
   * @private
   * @category Scrolling
   */
  onRowManagerRequestScrollChange({ bottom }) {
    this.scrollable.y = bottom - this.bodyHeight;
  }
  /**
   * Scroll syncing for normal headers & grid + triggers virtual rendering for vertical scroll
   * @private
   * @fires scroll
   * @category Scrolling
   */
  initScroll() {
    const me = this, { scrollable } = me;
    if (!me.scrollInitialized) {
      me.scrollInitialized = true;
      scrollable.contentElement = me.contentElement;
      scrollable.ion({
        scroll: "onGridVerticalScroll",
        scrollend: "onGridVerticalScrollEnd",
        thisObj: me
      });
      me.callEachSubGrid("initScroll");
      if (BrowserHelper.isMobileSafari) {
        scrollable.element.style.transform = "translate3d(0, 0, 0)";
      }
    }
  }
  onGridVerticalScroll({ source: scrollable }) {
    const me = this, { y: scrollTop } = scrollable;
    if (scrollTop !== me._scrollTop) {
      me._scrollTop = scrollTop;
      if (!me.scrolling) {
        me.scrolling = true;
        me.eachSubGrid((s) => s.suspendResizeMonitor = true);
      }
      me.rowManager.updateRenderedRows(scrollTop);
      me.afterScroll({ scrollTop });
      me.trigger("scroll", { scrollTop });
    }
  }
  onGridVerticalScrollEnd(e) {
    this.scrolling = false;
    this.eachSubGrid((s) => s.suspendResizeMonitor = false);
    this.trigger("verticalScrollEnd", e);
  }
  /**
   * Scrolls a locally available record's row into view. If row isn't rendered it tries to calculate position.
   * Accepts the {@link BryntumScrollOptions} `column` property
   * @param {Core.data.Model|String|Number} recordOrId Record or record id
   * @param {BryntumScrollOptions} [options] How to scroll.
   * @returns {Promise} A promise which resolves when the specified row has been scrolled into view.
   * @category Scrolling
   */
  async scrollRowIntoView(recordOrId, options = defaultScrollOptions3) {
    var _a5;
    const me = this, blockPosition = options.block || "nearest", { rowManager } = me, record = me.store.getById(recordOrId);
    if (record) {
      if (me.isLockedRows) {
        me.syncSplits((other) => other.scrollRowIntoView(...arguments));
      }
      let scrollPromise2;
      if (me.store.indexOf(record) === -1) {
        return resolvedPromise;
      }
      if (!me.rowManager.topRow) {
        await me.await("renderRows");
      }
      let scroller = me.scrollable, recordRect = me.getRecordCoords(record);
      const scrollerRect = Rectangle.from(scroller.element);
      if (recordRect.virtual) {
        const virtualBlock = recordRect.block, innerOptions = blockPosition !== "nearest" ? options : {
          block: virtualBlock
        };
        scrollPromise2 = scroller.scrollIntoView(recordRect, {
          block: "center"
        });
        rowManager.scrollTargetRecordId = record;
        rowManager.updateRenderedRows(scroller.y, true);
        recordRect = me.getRecordCoords(record);
        rowManager.lastScrollTop = scroller.y;
        if (recordRect.virtual) {
          return resolvedPromise;
        }
        if (options.animate) {
          scroller.suspendEvents();
          if (blockPosition === "end" || blockPosition === "nearest" && virtualBlock === "end") {
            scroller.y -= scrollerRect.bottom - recordRect.bottom;
          } else if (blockPosition === "start" || blockPosition === "nearest" && virtualBlock === "start") {
            scroller.y += recordRect.y - scrollerRect.y;
          }
          rowManager.updateRenderedRows(scroller.y, false, true);
          if (virtualBlock === "end") {
            scroller.y -= rowManager.appendRowBuffer * rowManager.rowHeight - 1;
          } else {
            scroller.y += rowManager.prependRowBuffer * rowManager.rowHeight - 1;
          }
          scroller.resumeEvents();
          await scroller.scrollIntoView(me.getRecordCoords(record), Object.assign({}, options, innerOptions));
        } else {
          if (!options.recursive) {
            await scrollPromise2;
          }
          await ((_a5 = me.scrollRowIntoView) == null ? void 0 : _a5.call(me, record, Object.assign({ recursive: true }, options, innerOptions)));
        }
      } else {
        let { column } = options;
        if (column) {
          if (!column.isModel) {
            column = me.columns.getById(column) || me.columns.get(column);
          }
          if (column) {
            scroller = me.getSubGridFromColumn(column).scrollable;
            const cellRect = Rectangle.from(rowManager.getRowFor(record).getCell(column.id));
            recordRect.x = cellRect.x;
            recordRect.width = cellRect.width;
          }
        } else {
          options = ObjectHelper.assign({}, options, { x: false });
        }
        await scroller.scrollIntoView(recordRect, options);
      }
    }
  }
  /**
   * Scrolls a column into view (if it is not already)
   * @param {Grid.column.Column|String|Number} column Column name (data) or column index or actual column object.
   * @param {BryntumScrollOptions} [options] How to scroll.
   * @returns {Promise} If the column exists, a promise which is resolved when the column header element has been
   * scrolled into view.
   * @category Scrolling
   */
  scrollColumnIntoView(column, options) {
    column = column instanceof Column ? column : this.columns.get(column) || this.columns.getById(column) || this.columns.getAt(column);
    const subGrid = this.getSubGridFromColumn(column);
    if (subGrid.columns.visibleColumns.length > 1) {
      return subGrid.scrollColumnIntoView(column, options);
    }
    return Promise.resolve();
  }
  /**
   * Scrolls a locally available record's cell into view (if it is not already).
   * @param {GridLocationConfig|Grid.util.GridLocation} cellContext Cell selector { id: recordId, column: 'columnName' }
   * @returns {Promise} A promise which resolves when the specified cell has been scrolled into view.
   * @category Scrolling
   */
  scrollCellIntoView(cellContext, options) {
    const location2 = this.normalizeCellContext(cellContext);
    return this.scrollRowIntoView(location2.id, {
      column: location2.columnId,
      ...typeof options === "boolean" ? { animate: options } : options
    });
  }
  /**
   * Scroll all the way down
   * @returns {Promise} A promise which resolves when the bottom is reached.
   * @category Scrolling
   * @non-lazy-load
   */
  scrollToBottom(options) {
    return this.scrollRowIntoView(this.isLockedRows ? this.originalStore.last : this.store.last, options);
  }
  /**
   * Scroll all the way up
   * @returns {Promise} A promise which resolves when the top is reached.
   * @category Scrolling
   */
  scrollToTop(options) {
    return this.scrollable.scrollBy(0, -this.scrollable.y, options);
  }
  /**
   * Stores the scroll state. Returns an objects with a `scrollTop` number value for the entire grid and a `scrollLeft`
   * object containing a left position scroll value per sub grid.
   * @returns {Object}
   * @category Scrolling
   */
  storeScroll() {
    const state = this.storedScrollState = {
      scrollTop: this.scrollable.scrollTopState || this.scrollable.y,
      scrollLeft: {}
    };
    this.eachSubGrid((subGrid) => {
      state.scrollLeft[subGrid.region] = subGrid.scrollable.x;
    });
    return state;
  }
  /**
   * Restore scroll state. If state is not specified, restores the last stored state.
   * @param {Object} [state] Scroll state, optional
   * @category Scrolling
   */
  restoreScroll(state = this.storedScrollState) {
    const me = this;
    me.eachSubGrid((subGrid) => {
      var _a5;
      const x = state.scrollLeft[subGrid.region];
      if (x != null) {
        subGrid.scrollable.updateX(x);
        subGrid.header.scrollable.updateX(x);
        subGrid.footer.scrollable.updateX(x);
        (_a5 = subGrid.fakeScroller) == null ? void 0 : _a5.updateX(x);
      }
    });
    me.restoreVerticalScroll(state);
  }
  restoreVerticalScroll(state = this.storedScrollState) {
    const { rowManager, scrollable } = this;
    if (rowManager.rowCount) {
      scrollable.updateY(state.scrollTop);
    } else {
      scrollable.scrollTopState = state.scrollTop;
      rowManager.ion({
        changeTotalHeight: () => this.restoreVerticalScroll(state),
        once: true
      });
    }
  }
  //endregion
  //region Theme & measuring
  beginGridMeasuring() {
    const me = this;
    me.element.classList.add("b-grid-measuring");
    if (!me.$measureCellElements) {
      me.$measureCellElements = DomHelper.createElement({
        // For row height measuring, features are not yet there. Work around that for the stripe feature,
        // which removes borders
        className: "b-grid-subgrid " + (!me._isRowMeasured && me.hasFeature("stripe") ? "b-stripe" : ""),
        reference: "subGridElement",
        style: {
          position: "absolute",
          top: "-10000px",
          left: "-100000px",
          visibility: "hidden",
          contain: "strict"
        },
        children: [
          {
            className: "b-grid-row",
            reference: "rowElement",
            children: [
              {
                className: "b-grid-cell",
                reference: "cellElement",
                style: {
                  width: "auto",
                  contain: BrowserHelper.isFirefox ? "layout paint" : "layout style paint"
                }
              }
            ]
          }
        ]
      });
    }
    me.getConfig("element");
    me.verticalScroller.appendChild(me.$measureCellElements.subGridElement);
    if (!me.rendered) {
      const targetEl = me.appendTo || me.insertBefore || document.body, rootElement = DomHelper.getRootElement(typeof targetEl === "string" ? document.getElementById(targetEl) : targetEl) || document.body;
      if (!me.adopt || !rootElement.contains(me.element)) {
        rootElement.appendChild(me.element);
        me.$removeAfterMeasuring = true;
      }
    }
    return me.$measureCellElements;
  }
  endGridMeasuring() {
    const me = this;
    me.element.classList.remove("b-grid-measuring");
    if (me.$removeAfterMeasuring) {
      me.element.remove();
      me.$removeAfterMeasuring = false;
    }
    me.$measureCellElements.subGridElement.remove();
  }
  /**
   * Creates a fake subgrid with one row and measures its height. Result is used as rowHeight.
   * @private
   */
  measureRowHeight() {
    const me = this, { rowElement } = me.beginGridMeasuring(), bodyZoom = parseFloat(DomHelper.getStyleValue(document.body, "zoom")) || 1, styles2 = DomHelper.getStyleValue(rowElement, ["height", "border-top-width", "border-bottom-width"]), styleHeight = parseInt(styles2.height), { devicePixelRatio } = DomHelper, multiplier = bodyZoom * (BrowserHelper.isFirefox ? devicePixelRatio / Math.max(Math.trunc(devicePixelRatio), 1) : 1), borderTop = styles2["border-top-width"] ? Math.round(multiplier * parseFloat(styles2["border-top-width"])) : 0, borderBottom = styles2["border-bottom-width"] ? Math.round(multiplier * parseFloat(styles2["border-bottom-width"])) : 0;
    if (me.rowHeight == null || me.rowHeight === me._rowHeightFromStyle) {
      me.rowHeight = !isNaN(styleHeight) && styleHeight ? styleHeight : me.defaultRowHeight;
      me._rowHeightFromStyle = me.rowHeight;
    }
    me._rowBorderHeight = borderTop + borderBottom;
    me._isRowMeasured = true;
    me.endGridMeasuring();
  }
  /**
   * Handler for global theme change event (triggered by shared.js). Remeasures row height.
   * @private
   */
  onThemeChange({ theme }) {
    this.whenVisible("measureRowHeight");
    this.trigger("theme", { theme });
  }
  //endregion
  //region Rendering of rows
  /**
   * Triggers a render of all the cells in the row for the passed record.
   *
   * Since Grid uses virtualized rows, calling this method for a record that is not currently displayed in a row will
   * not have any effect.
   *
   * {@note}Manipulating records and Grid settings refreshes the Grid automatically. You should only need to call
   * this function if you have outside data/settings unknown to the Grid that has changed and requires Grid to update
   * the row to reflect the changes{/@note}
   *
   * @param {Core.data.Model} record The record whose row should be refreshed
   * @category Rendering
   */
  refreshRow(record) {
    this.refreshRows([record]);
  }
  /**
   * Triggers a render of all the cells in the rows for the passed records. Leave the argument out to refresh all
   * rows.
   *
   * Since Grid uses virtualized rows, calling this method for records that are not currently displayed in rows will
   * not have any effect.
   *
   * {@note}Manipulating records and Grid settings refreshes the Grid automatically. You should only need to call
   * this function if you have outside data/settings unknown to the Grid that has changed and requires Grid to update
   * rows to reflect the changes{/@note}
   *
   * @param {Core.data.Model[]} [records] The records whose rows should be refreshed
   * @category Rendering
   */
  refreshRows(records = null, returnToTop = false) {
    if (this.refreshSuspended) {
      return;
    }
    const { element, rowManager } = this;
    if (!records) {
      element.classList.add("b-notransition");
      if (returnToTop) {
        rowManager.returnToTop();
      } else {
        rowManager.refresh(true);
      }
      element.classList.remove("b-notransition");
    } else {
      const rows = records.flatMap((record) => {
        var _a5;
        return (_a5 = rowManager.getRowById(record)) != null ? _a5 : [];
      });
      rowManager.renderRows(rows);
    }
  }
  /**
   * Triggers a render of all the cells in a column.
   * @param {Grid.column.Column} column
   * @param {Boolean} [enableTransitions=true] Set to `false` to disable transitions in cells
   * @category Rendering
   */
  refreshColumn(column, enableTransitions = true) {
    if (column.isVisible) {
      this.runWithTransition(() => {
        if (column.isLeaf) {
          this.rowManager.forEach((row) => row.renderCell(row.getCell(column.id)));
        } else {
          column.children.forEach((child) => this.refreshColumn(child));
        }
      }, enableTransitions);
    }
  }
  //endregion
  //region Render the grid
  /**
   * Recalculates virtual scrollbars widths and scrollWidth
   * @private
   */
  refreshVirtualScrollbars() {
    const me = this, {
      headerContainer,
      footerContainer,
      virtualScrollers,
      scrollable,
      hasVerticalOverflow
    } = me, { classList } = virtualScrollers, hadHorizontalOverflow = !classList.contains("b-hide-display"), hasHorizontalOverflow = Object.values(me.subGrids).some((subGrid) => subGrid.overflowingHorizontally), horizontalOverflowChanged = hasHorizontalOverflow !== hadHorizontalOverflow;
    if (horizontalOverflowChanged) {
      virtualScrollers.classList.toggle("b-hide-display", !hasHorizontalOverflow);
    }
    if (DomHelper.scrollBarWidth) {
      const needsPadding = hasVerticalOverflow || scrollable.overflowY === "scroll";
      headerContainer.classList.toggle("b-show-yscroll-padding", needsPadding);
      footerContainer.classList.toggle("b-show-yscroll-padding", needsPadding);
      virtualScrollers.classList.toggle("b-show-yscroll-padding", needsPadding);
      if (horizontalOverflowChanged || !me.$virtualScrollbarsRefreshed) {
        me.callEachSubGrid("refreshFakeScroll");
        me.onHeightChange();
      }
    }
  }
  get hasVerticalOverflow() {
    return this.scrollable.hasOverflow("y");
  }
  /**
   * Returns content height calculated from row manager
   * @private
   */
  get contentHeight() {
    const rowManager = this.rowManager;
    return Math.max(rowManager.totalHeight, rowManager.bottomRow ? rowManager.bottomRow.bottom : 0);
  }
  onContentChange() {
    const me = this, rowManager = me.rowManager;
    if (me.isVisible) {
      rowManager.estimateTotalHeight(me.autoHeight);
      me.paintListener = null;
      me.refreshTotalHeight(me.contentHeight);
      me.callEachSubGrid("refreshFakeScroll");
      me.onHeightChange();
    } else if (!me.paintListener) {
      me.paintListener = me.ion({
        paint: "onContentChange",
        once: true,
        thisObj: me
      });
    }
  }
  triggerPaint() {
    if (!this.isPainted) {
      this.refreshBodyRectangle();
    }
    super.triggerPaint();
  }
  onHeightChange() {
    const me = this;
    me.refreshBodyRectangle();
    me._bodyHeight = me.autoHeight ? me.contentHeight : me.bodyContainer.offsetHeight;
  }
  /**
   * Suspends UI refreshes after updates to the underlying data.
   *
   * Multiple calls to `suspendRefresh` stack up, and will require an equal number of `resumeRefresh` calls to
   * actually resume UI refresh.
   *
   * @category Rendering
   */
  suspendRefresh() {
    this.refreshSuspended++;
  }
  /**
   * Resumes UI refreshes after updates to the underlying data.
   *
   * Multiple calls to `suspendRefresh` stack up, and will require an equal number of `resumeRefresh` calls to
   * actually resume UI refresh.
   *
   * Specify `false` as the first param to not refresh if this call unblocked the refresh suspension.
   *
   * @param {Boolean} [trigger=true] `true` to trigger a refresh, if this resume unblocks suspension
   * @category Rendering
   */
  resumeRefresh(trigger = true) {
    if (this.refreshSuspended && !--this.refreshSuspended) {
      if (trigger) {
        this.refreshRows();
      }
      this.trigger("resumeRefresh", { trigger });
    }
  }
  /**
   * Rerenders all grid rows, completely replacing all row elements with new ones
   * @category Rendering
   */
  renderRows(keepScroll = true) {
    const me = this, scrollState = keepScroll && me.storeScroll();
    if (me.refreshSuspended) {
      return;
    }
    me.trigger("beforeRenderRows");
    me.renderingRows = true;
    me.element.classList.add("b-grid-refreshing");
    if (!keepScroll) {
      me.scrollable.y = me._scrollTop = 0;
    }
    me.rowManager.reinitialize(!keepScroll);
    me.trigger("renderRows");
    me.renderingRows = false;
    me.onContentChange();
    if (keepScroll) {
      me.restoreScroll(scrollState);
    }
    if (me.autoHeight && me.store.count > 200 && !me.autoHeightWarning && !me.disableAutoHeightWarning) {
      console.warn("Warning: 'autoHeight' is enabled for a large number of rows (200+). While 'autoHeight' provides flexibility, it should be used cautiously as it disables virtualization, leading to significant performance degradation with large datasets. Consider alternatives to maintain optimal performance.");
      me.autoHeightWarning = true;
    }
    me.element.classList.remove("b-grid-refreshing");
  }
  /**
   * Rerenders the grids rows, headers and footers, completely replacing all row elements with new ones
   * @category Rendering
   */
  renderContents() {
    const me = this, { element, headerContainer, footerContainer, rowManager } = me;
    me.emptyCache();
    if (me.isPainted) {
      me._headerHeight = null;
      me.callEachSubGrid("refreshHeader");
      me.callEachSubGrid("refreshFooter");
      me.renderHeader(headerContainer, element);
      me.renderFooter(footerContainer, element);
      me.fixSizes();
      const refreshContext = rowManager.removeAllRows();
      rowManager.calculateRowCount(false, true, true);
      rowManager.setPosition(refreshContext);
      me.renderRows();
    }
  }
  /**
   * Rerenders all grid headers
   * @category Rendering
   */
  refreshHeaders() {
    this.callEachSubGrid("refreshHeader");
  }
  /**
   * Rerender a single grid header
   * @param {Grid.column.Column} column The column to refresh
   * @category Rendering
   */
  refreshHeader(column) {
    column.subGrid.refreshHeader();
  }
  onPaintOverride() {
  }
  onStoreLoadOverride() {
  }
  // Render rows etc. on first paint, to make sure Grid's element has been laid out
  onInternalPaint({ firstPaint }) {
    var _a5;
    const me = this;
    me.ariaElement.setAttribute("aria-rowcount", me.store.count + 1);
    (_a5 = super.onInternalPaint) == null ? void 0 : _a5.call(this, ...arguments);
    if (me.onPaintOverride() || !firstPaint) {
      return;
    }
    const {
      rowManager,
      store,
      element,
      headerContainer,
      bodyContainer,
      footerContainer
    } = me, scrollPad = DomHelper.scrollBarPadElement;
    let columnsChanged, maxDepth = 0;
    me.role = (store == null ? void 0 : store.isTree) ? "treegrid" : "grid";
    me.columns.ion({
      change: () => columnsChanged = true,
      once: true
    });
    me.updateResponsive(me.width, 0);
    if (columnsChanged) {
      me.callEachSubGrid("refreshHeader", headerContainer);
      me.callEachSubGrid("refreshFooter", footerContainer);
    }
    me.renderHeader(headerContainer, element);
    me.renderFooter(footerContainer, element);
    DomHelper.append(headerContainer, scrollPad);
    DomHelper.append(footerContainer, scrollPad);
    DomHelper.append(me.virtualScrollers, scrollPad);
    me.refreshBodyRectangle();
    const bodyOffsetHeight = me.bodyContainer.offsetHeight;
    if (me.autoHeight) {
      me._bodyHeight = rowManager.initWithHeight(element.offsetHeight - headerContainer.offsetHeight - footerContainer.offsetHeight, true);
      bodyContainer.style.height = me.bodyHeight + "px";
    } else {
      me._bodyHeight = bodyOffsetHeight;
      rowManager.initWithHeight(me._bodyHeight, true);
    }
    me.eachSubGrid((subGrid) => {
      if (subGrid.header.maxDepth > maxDepth) {
        maxDepth = subGrid.header.maxDepth;
      }
    });
    headerContainer.dataset.maxDepth = maxDepth;
    me.fixSizes();
    if (store.count || !store.isLoading) {
      me.renderRows();
    }
    if (me.columns.usesAutoHeight) {
      const { fonts } = document;
      if ((fonts == null ? void 0 : fonts.status) !== "loaded") {
        fonts.ready.then(() => !me.isDestroyed && me.refreshRows());
      }
    }
    me.initScroll();
    me.initInternalEvents();
  }
  render() {
    var _a5;
    const me = this;
    me.requireSize = Boolean(me.owner);
    super.render(...arguments);
    me.setupFocusListeners();
    if (!me.autoHeight) {
      if (me.headerContainer.offsetHeight && !me.bodyContainer.offsetHeight) {
        console.warn("Grid element not sized correctly, please check your CSS styles and review how you size the widget");
      }
      if (!me.splitFrom && !((_a5 = me.features.split) == null ? void 0 : _a5.owner) && // Don't warn for splits
      !("minHeight" in me.initialConfig) && !("height" in me.initialConfig) && parseInt(globalThis.getComputedStyle(me.element).minHeight) === me.height) {
        console.warn(
          `The ${me.$$name} is sized by its predefined minHeight, likely this is not intended. Please check your CSS and review how you size the widget, or assign a fixed height in the config. For more information, see the "Basics/Sizing the component" guide in docs.`
        );
      }
    }
  }
  //endregion
  //region Hooks
  /**
   * Called after headers have been rendered to the headerContainer.
   * This does not do anything, it's just for Features to hook in to.
   * @param {HTMLElement} headerContainer DOM element which contains the headers.
   * @param {HTMLElement} element Grid element
   * @private
   * @category Rendering
   */
  renderHeader(headerContainer, element) {
  }
  /**
   * Called after footers have been rendered to the footerContainer.
   * This does not do anything, it's just for Features to hook in to.
   * @param {HTMLElement} footerContainer DOM element which contains the footers.
   * @param {HTMLElement} element Grid element
   * @private
   * @category Rendering
   */
  renderFooter(footerContainer, element) {
  }
  // Hook for features to affect cell rendering before renderers are run
  beforeRenderCell() {
  }
  // Hooks for features to react to a row being rendered
  beforeRenderRow() {
  }
  afterRenderRow() {
  }
  // Hook for features to react to scroll
  afterScroll() {
  }
  // Hook that can be overridden to prepare custom editors, can be used by framework wrappers
  processCellEditor(editorConfig) {
  }
  // Hook for features to react to column changes
  afterColumnsChange() {
    super.afterColumnsChange(...arguments);
  }
  // Hook for features to react to record removal (which might be transitioned)
  afterRemove(removeEvent) {
  }
  // Hook for features to react to groups being collapsed/expanded
  afterToggleGroup() {
  }
  // Hook for features to react to subgrid being collapsed
  afterToggleSubGrid() {
  }
  // Hook into Base, to trigger another hook for features to hook into :)
  // If features hook directly into this, it will be called both for Grid's changes + feature's changes,
  // since they also extend Base.
  onConfigChange(info) {
    super.onConfigChange(info);
    if (!this.isConfiguring) {
      this.afterConfigChange(info);
    }
  }
  afterPluginConfigChange() {
  }
  afterConfigChange(info) {
  }
  afterAddListener(eventName, listener) {
  }
  afterRemoveListener(eventName, listener) {
  }
  //endregion
  //region Masking and Appearance
  syncMaskCover(mask = this.masked) {
    if (mask) {
      const bodyRect = mask.cover === "body" && this.rectangleOf("bodyContainer"), scrollerRect = bodyRect && DomHelper.scrollBarWidth && this.rectangleOf("virtualScrollers"), { style } = mask.element;
      style.marginTop = bodyRect ? `${bodyRect.y}px` : "";
      style.height = bodyRect ? `${bodyRect.height + ((scrollerRect == null ? void 0 : scrollerRect.height) || 0)}px` : "";
    }
  }
  changeMasked(mask, maskInstance) {
    if (mask) {
      const me = this;
      if (typeof mask === "string") {
        mask = { text: mask };
      }
      if (me.isLockedRows || me.splitFrom) {
        mask.cover = null;
        mask.target = me.element.parentElement;
      } else {
        mask.cover = "body";
        mask.target = me.element;
      }
    }
    return super.changeMasked(mask, maskInstance);
    ;
  }
  /**
   * Show a load mask with a spinner and the specified message. When using an AjaxStore masking and unmasking is
   * handled automatically, but if you are loading data in other ways you can call this function manually when your
   * load starts.
   * ```
   * myLoadFunction() {
   *   // Show mask before initiating loading
   *   grid.maskBody('Loading data');
   *   // Your custom loading code
   *   load.then(() => {
   *      // Hide the mask when loading is finished
   *      grid.unmaskBody();
   *   });
   * }
   * ```
   * @param {String|MaskConfig} loadMask The message to show in the load mask (next to the spinner) or a config object
   * for a {@link Core.widget.Mask}.
   * @returns {Core.widget.Mask}
   * @category Misc
   */
  maskBody(loadMask) {
    let ret;
    if (this.bodyContainer) {
      this.masked = Mask.mergeConfigs(this.loadMaskDefaults, loadMask);
      ret = this.masked;
    }
    return ret;
  }
  /**
   * Hide the load mask.
   * @category Misc
   */
  unmaskBody() {
    this.masked = null;
  }
  updateEmptyText(emptyText) {
    var _a5, _b2;
    (_a5 = this.emptyTextEl) == null ? void 0 : _a5.remove();
    const isDomHelper = typeof emptyText === "object";
    if (isDomHelper) {
      emptyText = ArrayHelper.asArray(emptyText);
    }
    this.emptyTextEl = DomHelper.createElement({
      parent: (_b2 = this.firstItem) == null ? void 0 : _b2.element,
      className: "b-empty-text",
      [isDomHelper ? "children" : (emptyText == null ? void 0 : emptyText.includes("<")) ? "html" : "text"]: emptyText
    });
  }
  toggleEmptyText() {
    const me = this, store = me.isLockedRowsView ? me.store.$master : me.store, isEmpty = !(store.count > 0 || store.isLoading || store.isCommitting), storeIsEmpty = Boolean(!store.count && !store.isFiltered || store.isLoading || store.isCommitting);
    me.element.classList.toggle("b-grid-empty", isEmpty);
    me.announceAriaLive(storeIsEmpty ? me.emptyText : store.isFiltered ? me.L("L{filteredRecords}", store.count) : "");
  }
  // Notify columns when our read-only state is toggled
  updateReadOnly(readOnly, old) {
    var _a5;
    super.updateReadOnly(readOnly, old);
    if (!this.isConfiguring) {
      for (const column of this.columns.bottomColumns) {
        (_a5 = column.updateReadOnly) == null ? void 0 : _a5.call(column, readOnly);
      }
    }
  }
  //endregion
  //region Extract config
  // This function is not meant to be called by any code other than Base#getCurrentConfig().
  // It extracts the current configs for the grid, with special handling for inline data
  getCurrentConfig(options) {
    const result = super.getCurrentConfig(options), { store } = this, data = store.getInlineData(options), storeState = store.getCurrentConfig(options) || result.store;
    if (data.length) {
      result.data = data;
    }
    if (storeState && store.originalModelClass === GridRowModel) {
      delete storeState.modelClass;
    }
    if (!ObjectHelper.isEmpty(storeState)) {
      result.store = storeState;
    }
    if (result.store) {
      delete result.store.data;
    }
    return result;
  }
  /**
   * Suspends CSS transitions after a row / event has been updated
   *
   * Multiple calls to `suspendAnimations` stack up, and will require an equal number of `resumeAnimations` calls to
   * actually resume animations.
   * @category Misc
   */
  suspendAnimations() {
    this._animationSuspendedCounter++;
  }
  /**
   * Resumes CSS transitions after a row / event has been updated
   * @category Misc
   */
  resumeAnimations() {
    this._animationSuspendedCounter--;
  }
  /**
   * Runs a function with transitions enabled (row height, event size etc.). Useful if you want to alter the UI
   * state with a transition.
   *
   * @param {Function} fn The function to run
   * @privateparam {Boolean} shouldAnimate boolean if it is false, animation won't run, otherwise animation runs
   * @returns {Promise} A promise which resolves when the transition duration has expired
   * @category Misc
   */
  async runWithTransition(fn2, shouldAnimate) {
    const me = this;
    if (!shouldAnimate || !me.isVisible || me._animationSuspendedCounter !== 0 || me.enableEventAnimations === false) {
      return fn2();
    }
    me.isAnimating = true;
    await me.executeAndAwaitAnimations(me.element, fn2);
    me.isAnimating = false;
    if (!me.isDestroyed && !me.isAnimating) {
      me.trigger("transitionEnd");
    }
  }
  // Wait for conditions that might prevent animations from running, such as the TreeGroup asynchronously
  // reforming the tree
  async waitForReadiness() {
    var _a5, _b2;
    await ((_b2 = (_a5 = this.features.treeGroup) == null ? void 0 : _a5.waitForReadiness) == null ? void 0 : _b2.call(_a5));
  }
};
//region Config
__publicField(GridBase, "$name", "GridBase");
// Factoryable type name
__publicField(GridBase, "type", "gridbase");
__publicField(GridBase, "bindStoreChangeset", true);
GridBase.initClass();
VersionHelper.setVersion("grid", "6.2.5");
GridBase._$name = "GridBase";

// ../Grid/lib/Grid/view/Grid.js
var Grid = class extends GridBase {
};
__publicField(Grid, "$name", "Grid");
// Factoryable type name
__publicField(Grid, "type", "grid");
Grid.initClass();
Grid._$name = "Grid";

// ../Grid/lib/Grid/view/TreeGrid.js
var TreeGrid = class extends Grid {
  static get configurable() {
    return {
      /**
       * The store instance or config object that holds the records to be displayed by this TreeGrid. If assigning
       * a store instance, it must be configured with `tree: true`.
       *
       * A store will be created if none is specified.
       * @config {Core.data.Store|StoreConfig} store
       * @category Common
       */
      store: {
        tree: true
      }
    };
  }
  //region Plugged in functions / inherited configs
  /**
   * Collapse an expanded node or expand a collapsed. Optionally forcing a certain state.
   *
   * @function toggleCollapse
   * @param {String|Number|Core.data.Model} idOrRecord Record (the node itself) or id of a node to toggle
   * @param {Boolean} [collapse] Force collapse (true) or expand (false)
   * @param {Boolean} [skipRefresh] Set to `true` to not refresh rows (if calling in batch)
   * @async
   * @non-lazy-load
   * @category Feature shortcuts
   */
  /**
   * Collapse a single node.
   *
   * @function collapse
   * @param {String|Number|Core.data.Model} idOrRecord Record (the node itself) or id of a node to collapse
   * @async
   * @non-lazy-load
   * @category Feature shortcuts
   */
  /**
   * Expand a single node.
   *
   * @function expand
   * @param {String|Number|Core.data.Model} idOrRecord Record (the node itself) or id of a node to expand
   * @async
   * @non-lazy-load
   * @category Feature shortcuts
   */
  /**
   * Expands parent nodes to make this node "visible".
   *
   * @function expandTo
   * @param {String|Number|Core.data.Model} idOrRecord Record (the node itself) or id of a node
   * @async
   * @non-lazy-load
   * @category Feature shortcuts
   */
  //endregion
  /* disconnect doc comment */
  //region Store
  updateStore(store, was) {
    if (store && !(store.tree || store.isTree)) {
      throw new Error("TreeGrid requires a Store configured with tree : true");
    }
    super.updateStore(store, was);
  }
  //endregion
};
__publicField(TreeGrid, "$name", "TreeGrid");
// Factoryable type name
__publicField(TreeGrid, "type", "treegrid");
TreeGrid.initClass();
TreeGrid._$name = "TreeGrid";

// ../Grid/lib/Grid/widget/GroupBar.js
var GroupBar = class extends ChipView {
  construct() {
    super.construct(...arguments);
    const grid = this.grid = this.up("gridbase", true);
    if (!grid) {
      throw new Error("GroupBar must be used inside a Grid component");
    }
    grid.ion({
      paint: this.onGridPaint,
      once: true,
      thisObj: this
    });
  }
  onGridPaint() {
    const me = this, { grid } = me, {
      treeGroup,
      group,
      columnReorder,
      tree
    } = grid.features;
    if (!treeGroup && !group) {
      throw new Error("GroupBar widget requires the TreeGroup or Group feature to be present");
    }
    columnReorder.usingGroupBarWidget = true;
    me.store = new Store({
      fields: ["cls", "ascending"],
      internalListeners: {
        add: me.onStoreChanged,
        remove: me.onStoreChanged,
        thisObj: me
      }
    });
    grid.ion({
      [tree ? "treeGroupChange" : void 0]: me.onTreeGroupChanged,
      beforeColumnDropFinalize: me.onBeforeColumnDropFinalize,
      columnDrag: me.onColumnDrag,
      columnDragStart: me.onColumnDragStart,
      columnDrop: me.onColumnDrop,
      thisObj: me
    });
    if (tree) {
      me.onTreeGroupChanged({ levels: treeGroup.levels });
    } else {
      grid.store.ion({
        group: me.onGroupChanged,
        thisObj: me
      });
      me.onGroupChanged();
    }
  }
  onStoreChanged({ records }) {
    var _a5;
    const me = this, { grid } = me;
    if (!grid.isConstructing && ((_a5 = records == null ? void 0 : records[0]) == null ? void 0 : _a5.cls) !== "b-drop-target") {
      me.ignoreGroupChange = true;
      if (grid.features.treeGroup) {
        grid.features.treeGroup.levels = me.store.map(({ field }) => field);
      } else if (me.store.count > 0) {
        grid.store.group(me.store.records[0].field);
      } else {
        grid.store.clearGroupers();
      }
      me.ignoreGroupChange = false;
    }
  }
  onGroupChanged() {
    const { groupers } = this.grid.store;
    this.store.data = groupers ? groupers.map((grouper) => this.grid.columns.get(grouper.field)) : [];
  }
  onTreeGroupChanged({ levels }) {
    if (!this.ignoreGroupChange) {
      if (levels.some((level) => level instanceof Function && !level.fieldName)) {
        throw new Error("GroupBar only supports column grouping");
      }
      this.store.data = (levels || []).map((level) => {
        level = level.fieldName || level;
        return this.grid.columns.get(level);
      });
    }
  }
  onColumnDragStart({ column }) {
    this.store.add({ id: "placeholder", cls: "b-drop-target", text: this.grid.features.treeGroup ? void 0 : column.text });
  }
  onColumnDrag({ context, column, event }) {
    const overGroupBar = event.target.closest(".b-groupbar");
    if (overGroupBar) {
      context.valid = column.groupable;
    }
  }
  onBeforeColumnDropFinalize({ column, event }) {
    const isTree = this.grid.features.tree, droppedOnGroupBar = event.target.closest(".b-groupbar");
    if (droppedOnGroupBar && column.groupable) {
      if (!isTree || !column.isTreeColumn) {
        this.store.getById("placeholder").remove();
        if (!isTree) {
          this.store.removeAll(true);
        }
        this.store.add(column);
      }
    }
  }
  onColumnDrop() {
    var _a5;
    (_a5 = this.store.getById("placeholder")) == null ? void 0 : _a5.remove();
  }
};
__publicField(GroupBar, "type", "groupbar");
__publicField(GroupBar, "$name", "GroupBar");
__publicField(GroupBar, "configurable", {
  selectedCls: "not-used",
  ariaLabel: "L{TreeGroup.group}",
  itemTpl(record) {
    return StringHelper.encodeHtml(StringHelper.capitalize(record.getValue(this.displayField)));
  }
});
GroupBar.initClass();
GroupBar._$name = "GroupBar";

// ../Grid/lib/Grid/widget/TreeCombo.js
var TreeCombo = class extends Combo {
  changePicker(picker, oldPicker) {
    if (!this.isDestroying && !picker.columns) {
      console.warn("No `columns` provided for the TreeCombo `picker` config");
      picker.columns = [{ type: "tree" }];
    }
    return super.changePicker(picker ? {
      ...picker,
      selectedRecordCollection: this.valueCollection
    } : picker, oldPicker);
  }
  updateMultiSelect(multiSelect) {
    super.updateMultiSelect(...arguments);
    this.picker.selectionMode.multiSelect = multiSelect;
  }
  updateReadOnly(readOnly) {
    super.updateReadOnly(...arguments);
    if (this._picker) {
      this.picker.readOnly = readOnly;
    }
  }
};
__publicField(TreeCombo, "$name", "TreeCombo");
__publicField(TreeCombo, "type", "treecombo");
__publicField(TreeCombo, "configurable", {
  multiSelect: true,
  picker: {
    type: "treegrid",
    minWidth: "35em",
    disableGridRowModelWarning: true,
    selectionMode: {
      row: true,
      checkbox: true
    }
  },
  chipView: {
    itemTpl(record) {
      return StringHelper.xss`${record.name}`;
    },
    scrollable: {
      overflowX: "hidden-scroll"
    }
  }
});
TreeCombo.initClass();
TreeCombo._$name = "TreeCombo";

// ../Scheduler/lib/Scheduler/column/DurationColumn.js
var DurationColumn = class extends NumberColumn {
  constructor() {
    super(...arguments);
    __publicField(this, "compositeField", true);
  }
  construct() {
    super.construct(...arguments);
    const sortFn = this.sortable;
    this.sortable = (...args) => sortFn.call(this, ...args);
  }
  get defaultEditor() {
    const { max: max3, min: min4, step, largeStep, decimalPrecision = this.grid.durationDisplayPrecision } = this;
    return ObjectHelper.cleanupProperties({
      type: "duration",
      name: this.field,
      max: max3,
      min: min4,
      step,
      decimalPrecision,
      largeStep
    });
  }
  //endregion
  //region Internal
  get durationUnitField() {
    return `${this.field}Unit`;
  }
  // Necessary to keep the formatter in sync with the configured decimal precision
  get formatter() {
    const me = this, { format: format2, _formatter: formatter, decimalPrecision } = me;
    if (!formatter || me._lastFormat !== format2) {
      me._formatter = NumberHelper.getLocalizedFormatter(
        decimalPrecision ? { maximumFractionDigits: decimalPrecision } : me._lastFormat = format2
      );
    }
    return me._formatter;
  }
  roundValue(duration) {
    var _a5, _b2;
    const multiplier = Math.pow(10, (_b2 = (_a5 = this.decimalPrecision) != null ? _a5 : this.grid.durationDisplayPrecision) != null ? _b2 : 1);
    return Math.round(duration * multiplier) / multiplier;
  }
  formatValue(duration, durationUnit) {
    if (duration instanceof Duration) {
      durationUnit = duration.unit;
      duration = duration.magnitude;
    }
    duration = this.roundValue(duration);
    const unitFn = this.abbreviatedUnit ? "getShortNameOfUnit" : "getLocalizedNameOfUnit";
    return super.formatValue(duration) + (this.abbreviatedUnit ? "" : " ") + DateHelper[unitFn](durationUnit, duration !== 1);
  }
  //endregion
  //region Render
  defaultRenderer({ value: value2, record, isExport, dataField }) {
    const type = typeof value2, durationValue = type === "number" ? value2 : value2 == null ? void 0 : value2.magnitude, durationUnit = type === "number" ? record.getValue(this.durationUnitField) : value2 == null ? void 0 : value2.unit;
    if (typeof durationValue !== "number") {
      return isExport ? "" : null;
    }
    if (!this.fieldWarningShown && dataField instanceof NumberDataField && this.type !== "totalslack") {
      console.warn(
        `Warning: Setting 'duration' as the 'field' value for the '${this.type}' column is not allowed since the field needs both a duration magnitude and unit, as supplied by for example the 'fullDuration' field. Additionally, a custom implemented field for the duration column should return a duration object to work correctly.`
      );
      this.fieldWarningShown = true;
    }
    return this.formatValue(durationValue, durationUnit);
  }
  //endregion
  // Used with CellCopyPaste as fullDuration doesn't work via record.get
  toClipboardString({ record }) {
    var _a5;
    return (_a5 = record.getValue(this.field)) == null ? void 0 : _a5.toString();
  }
  fromClipboardString({ string: string2, record }) {
    const duration = DateHelper.parseDuration(string2, true, this.durationUnit);
    if (duration && "magnitude" in duration) {
      return duration;
    }
    return record.fullDuration;
  }
  calculateFillValue({ value: value2, record }) {
    return this.fromClipboardString({ string: value2, record });
  }
};
//region Config
__publicField(DurationColumn, "$name", "DurationColumn");
__publicField(DurationColumn, "type", "duration");
__publicField(DurationColumn, "isGanttColumn", true);
__publicField(DurationColumn, "fields", [
  /**
   * Precision of displayed duration, defaults to use {@link Scheduler.view.Scheduler#config-durationDisplayPrecision}.
   * Specify an integer value to override that setting, or `false` to use raw value
   * @config {Number|Boolean} decimalPrecision
   */
  { name: "decimalPrecision" },
  "abbreviatedUnit"
]);
__publicField(DurationColumn, "defaults", {
  /**
   * Set to `true` to display duration in short form (5d, 2mo)
   * @config {Boolean}
   */
  abbreviatedUnit: null,
  min: null,
  max: null,
  step: 1,
  largeStep: 0,
  /**
   * The {@link Core.data.field.DataField#config-name} of the {@link Core.data.Model data model} field to
   * read a cells value from.
   *
   * <div class="note">Setting <b>duration</b> as the <b>field</b> value for the duration column is not allowed
   * since the field needs both a duration magnitude and unit, as supplied by for example the
   * {@link Scheduler/model/TimeSpan#field-fullDuration} field. Additionally, a custom implemented field for
   * the duration column should return a duration object to work correctly.</div>
   *
   * @prp {String} field
   * @default 'fullDuration'
   * @category Common
   */
  field: "fullDuration",
  text: "L{Duration}",
  instantUpdate: true,
  // Undocumented, used by Filter feature to get type of the filter field
  filterType: "duration",
  sortable(durationEntity1, durationEntity2) {
    const ms1 = durationEntity1.getValue(this.field), ms2 = durationEntity2.getValue(this.field);
    return ms1 - ms2;
  }
});
ColumnStore.registerColumnType(DurationColumn);
DurationColumn._$name = "DurationColumn";

// ../Scheduler/lib/Scheduler/model/mixin/TimeZonedDatesMixin.js
var dateFieldsToConvert = {
  startDate: 1,
  endDate: 1,
  constraintDate: 1,
  deadlineDate: 1
};
var TimeZonedDatesMixin_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    get timeZone() {
      return this.getData("timeZone");
    }
    set timeZone(timeZone) {
      this.setData("timeZone", timeZone);
    }
    static get timeZoneDateFields() {
      return dateFieldsToConvert;
    }
    /**
     * Returns `true` if the field is adjusted by `timeZone` config.
     * @param {String} fieldName Name of a field
     * @returns {Boolean} `true` if the field is adjusted by `timeZone` config.
     */
    static isTimeZoneDateField(fieldName) {
      return Boolean(this.timeZoneDateFields[fieldName]);
    }
    setLocalDate(field, date2) {
      const me = this;
      if (me.timeZone != null) {
        date2 = TimeZoneHelper.toTimeZone(date2, me.timeZone);
      }
      me.set(field, date2, true);
      return date2;
    }
    getLocalDate(field) {
      if (this.timeZone != null && this[field] && !isNaN(this[field])) {
        return TimeZoneHelper.fromTimeZone(this[field], this.timeZone);
      }
      return this[field];
    }
    applyChangeset(rawChanges) {
      if (this.timeZone != null) {
        delete rawChanges.timeZone;
        for (const field in this.constructor.timeZoneDateFields) {
          if (rawChanges[field]) {
            const value2 = this.setLocalDate(field, new Date(rawChanges[field]));
            rawChanges[field] = value2;
          }
        }
      }
      return super.applyChangeset(...arguments);
    }
    getFieldPersistentValue(field) {
      var _a6, _b2;
      if (this.timeZone != null) {
        const fieldName = (_b2 = (_a6 = field == null ? void 0 : field.field) != null ? _a6 : field == null ? void 0 : field.name) != null ? _b2 : field, self2 = this.constructor;
        if (self2.isTimeZoneDateField(fieldName)) {
          const date2 = this.getLocalDate(fieldName);
          return field.serialize ? field.serialize(date2) : date2;
        }
      }
      return super.getFieldPersistentValue(field);
    }
    static processFieldData(field, converted, rawValue, data, record) {
      const { timeZone } = record;
      if (converted && timeZone && timeZone !== data.timeZone && this.isTimeZoneDateField(field.name || field)) {
        converted = TimeZoneHelper.toTimeZone(converted, timeZone);
      }
      return converted;
    }
    // Converts current record into a timeZone
    convertToTimeZone(timeZone, ignoreFields) {
      var _a6;
      if (this.timeZone === timeZone) {
        return;
      }
      const me = this, metaModified = { ...me.meta.modified }, convertFields = { ...me.constructor.timeZoneDateFields }, currentTimeZone = me.timeZone, isEngineModel = Boolean(me.$$);
      if (me.isTask && !me.manuallyScheduled) {
        delete convertFields.startDate;
        delete convertFields.endDate;
      }
      ignoreFields == null ? void 0 : ignoreFields.forEach((field) => {
        delete convertFields[field];
      });
      for (const field in convertFields) {
        if (me[field] != null) {
          if (isEngineModel) {
            me["_" + field] = null;
          }
          convertFields[field] = me[field];
          if (currentTimeZone != null) {
            convertFields[field] = me.getLocalDate(field);
            if (metaModified[field]) {
              metaModified[field] = TimeZoneHelper.fromTimeZone(metaModified[field], currentTimeZone);
            }
          }
        } else {
          delete convertFields[field];
        }
      }
      me.timeZone = timeZone;
      for (const field in convertFields) {
        me.setLocalDate(field, convertFields[field]);
        convertFields[field] = 1;
        if (timeZone != null && metaModified[field]) {
          metaModified[field] = TimeZoneHelper.toTimeZone(metaModified[field], timeZone);
        }
      }
      me.internalClearChanges(true, true, convertFields);
      if (!ObjectHelper.isEmpty(metaModified)) {
        me.meta.modified = metaModified;
        (_a6 = me.stores) == null ? void 0 : _a6.forEach((store) => store.modified.add(me));
      }
    }
  }, __publicField(_a5, "$name", "TimeZonedDatesMixin"), __publicField(_a5, "fields", [
    /**
     * The current timeZone this record is converted to. Used internally to keep track of time zone conversions.
     *
     * Can also be used to create a new record with dates in a specific non-local timezone. That is useful for
     * example when replacing a store dataset. That would be interpreted as a new load, and all dates would be
     * converted to the configured timezone.
     *
     * If specifically set to `null` when adding a new record to a Store, the new record's dates will be converted
     * to the Project's configured timezone.
     *
     * For more information about timezone conversion, se {@link Scheduler.model.ProjectModel#config-timeZone}.
     *
     * This field will not {@link Core.data.field.DataField#config-persist} by default.
     *
     * @field {String|Number|null} timeZone
     * @category Advanced
     */
    {
      name: "timeZone",
      persist: false
    }
  ]), _a5;
};

// ../Scheduler/lib/Scheduler/model/TimeSpan.js
var TimeSpan = class extends Model.mixin(TimeZonedDatesMixin_default) {
  //endregion
  //region Init
  construct(data, ...args) {
    const me = this;
    if (data == null ? void 0 : data.fullDuration) {
      const { magnitude, unit } = data.fullDuration;
      data.duration = magnitude;
      data.unit = unit;
      delete data.fullDuration;
    }
    super.construct(data, ...args);
    me.normalize();
    if (me.startDateMS && me.endDateMS && me.startDateMS > me.endDateMS) {
      console.error(`startDate > endDate for ${me.constructor.$name} record with id: ${me.id}`);
    }
  }
  //endregion
  //region Date normalization
  internalCalculateStartDate(endDate, duration, durationUnit) {
    return DateHelper.add(endDate, -duration, durationUnit);
  }
  internalCalculateEndDate(startDate, duration, durationUnit) {
    return DateHelper.add(startDate, duration, durationUnit);
  }
  internalCalculateDuration(startDate, endDate, durationUnit) {
    return DateHelper.as(durationUnit, DateHelper.diff(startDate, endDate, "h"), "h");
  }
  // Separate fn to allow calling later with specific values (used by Baseline)
  internalNormalize(startDate, endDate, duration, durationUnit) {
    const me = this, hasDuration = duration != null;
    if (startDate && endDate && !hasDuration) {
      me.setData("duration", me.internalCalculateDuration(startDate, endDate, durationUnit));
    } else if (startDate && !endDate && hasDuration) {
      me.setData("endDate", me.internalCalculateEndDate(startDate, duration, durationUnit));
    } else if (!startDate && endDate && hasDuration) {
      me.setData("startDate", me.internalCalculateStartDate(endDate, duration, durationUnit));
    }
  }
  normalize() {
    this.internalNormalize(
      this.startDate,
      this.endDate,
      this.duration,
      this.durationUnit || this.constructor.defaultValues.durationUnit
      // Default might not be applied yet
    );
  }
  //endregion
  //region Getters & Setters
  /**
   * Returns the event store this event is part of, if any.
   *
   * @property {Scheduler.data.EventStore}
   * @readonly
   * @category Misc
   */
  get eventStore() {
    var _a5;
    const me = this;
    if (me.isOccurrence) {
      return me.recurringTimeSpan.eventStore;
    }
    if (!me._eventStore) {
      me._eventStore = (_a5 = me.stores) == null ? void 0 : _a5.find((s) => s.isEventStore);
    }
    return me._eventStore;
  }
  updateInternalCls(cls) {
    if (this._cls) {
      this._cls.value = cls;
    } else {
      this._cls = new DomClassList(cls);
    }
  }
  set internalCls(cls) {
    this.updateInternalCls(cls);
    this.set("cls", this._cls.value);
  }
  get internalCls() {
    const { cls } = this;
    if (cls == null ? void 0 : cls.isDomClassList) {
      return cls;
    }
    this.updateInternalCls(cls);
    this.setData("cls", this._cls.value);
    return this._cls;
  }
  get cls() {
    if (!this._cls) {
      this._cls = new DomClassList(super.get("cls"));
    }
    return this._cls;
  }
  set cls(cls) {
    this.internalCls = cls;
  }
  get startDate() {
    return this.get("startDate");
  }
  set startDate(date2) {
    this.setStartDate(date2);
  }
  get endDate() {
    return this.get("endDate");
  }
  set endDate(date2) {
    this.setEndDate(date2);
  }
  get endingDate() {
    const me = this, {
      endDate,
      startDate
    } = me;
    if (endDate) {
      return endDate;
    }
    return DateHelper.add(startDate, me.duration, me.durationUnit);
  }
  get duration() {
    return this.get("duration");
  }
  set duration(duration) {
    this.setDuration(duration, this.durationUnit);
  }
  get durationUnit() {
    return this.get("durationUnit");
  }
  /**
   * Sets duration and durationUnit in one go. Only allowed way to change durationUnit, the durationUnit field is
   * readonly after creation
   * @param {Number} duration Duration value
   * @param {DurationUnit} durationUnit Unit for
   * specified duration value, see {@link #field-durationUnit} for valid values
   * @category Scheduling
   */
  setDuration(duration, durationUnit = this.durationUnit) {
    duration = parseFloat(duration);
    this.set({
      duration,
      durationUnit,
      ...this.updateDatesFromDuration(duration, durationUnit)
    });
  }
  updateDatesFromDuration(magnitude, unit, startDate = this.startDate, endDate = this.endDate) {
    const result = {};
    if (startDate) {
      result.endDate = this.internalCalculateEndDate(startDate, magnitude, unit);
    } else if (endDate) {
      result.startDate = this.internalCalculateStartDate(endDate, magnitude, unit);
    }
    return result;
  }
  /**
   * Returns duration of the event in given unit. This is a wrapper for {@link Core.helper.DateHelper#function-getDurationInUnit-static}
   * @param {DurationUnitShort} unit
   * @param {Boolean} [doNotRound]
   * @private
   * @returns {Number}
   */
  getDurationInUnit(unit, doNotRound) {
    const me = this;
    if (me.startDate && me.endDate) {
      return DateHelper.getDurationInUnit(me.startDate, me.endDate, unit, doNotRound);
    } else {
      return DateHelper.as(unit, me.duration, me.durationUnit);
    }
  }
  get fullDuration() {
    return new Duration({
      unit: this.durationUnit,
      magnitude: this.duration
    });
  }
  set fullDuration(duration) {
    if (duration == null) {
      this.set({ duration });
      return;
    }
    if (typeof duration === "string") {
      duration = DateHelper.parseDuration(duration, true, this.durationUnit);
    }
    this.setDuration(duration.magnitude, duration.unit);
  }
  /**
   * Sets the range start date
   *
   * @param {Date} date The new start date
   * @param {Boolean} keepDuration Pass `true` to keep the duration of the task ("move" the event), `false` to change the duration ("resize" the event).
   * Defaults to `true`
   * @category Scheduling
   */
  setStartDate(date2, keepDuration = true) {
    const me = this, toSet = {
      startDate: date2
    };
    if (date2) {
      let calcEndDate;
      if (keepDuration) {
        calcEndDate = me.duration != null;
      } else {
        if (me.endDate) {
          toSet.duration = me.internalCalculateDuration(date2, me.endDate, me.durationUnit);
          if (toSet.duration < 0) {
            throw new Error("Negative duration");
          }
        } else {
          calcEndDate = me.duration != null;
        }
      }
      if (calcEndDate) {
        toSet.endDate = me.internalCalculateEndDate(date2, me.duration, me.durationUnit);
      }
    } else {
      toSet.duration = null;
    }
    me.set(toSet);
  }
  /**
   * Sets the range end date
   *
   * @param {Date} date The new end date
   * @param {Boolean} keepDuration Pass `true` to keep the duration of the task ("move" the event), `false` to change the duration ("resize" the event).
   * Defaults to `false`
   * @category Scheduling
   */
  setEndDate(date2, keepDuration = false) {
    const me = this, toSet = {
      endDate: date2
    };
    if (date2) {
      let calcStartDate;
      if (keepDuration === true) {
        calcStartDate = me.duration != null;
      } else {
        if (me.startDate) {
          toSet.duration = me.internalCalculateDuration(me.startDate, date2, me.durationUnit);
          if (toSet.duration < 0)
            throw new Error("Negative duration");
        } else {
          calcStartDate = this.duration != null;
        }
      }
      if (calcStartDate) {
        toSet.startDate = me.internalCalculateStartDate(date2, me.duration, me.durationUnit);
      }
    }
    me.set(toSet);
  }
  /**
   * Sets the event start and end dates
   *
   * @param {Date} start The new start date
   * @param {Date} end The new end date
   * @param {Boolean} [silent] Pass `true` to not trigger events
   * @category Scheduling
   */
  setStartEndDate(start, end, silent) {
    if (start > end) {
      throw new Error("Start date must be less or equal to end date");
    }
    this.set({
      startDate: start,
      endDate: end
    }, null, silent);
  }
  /**
   * Returns an array of dates in this range. If the range starts/ends not at the beginning of day, the whole day will be included.
   * @readonly
   * @property {Date[]}
   * @category Scheduling
   */
  get dates() {
    const dates = [], startDate = DateHelper.startOf(this.startDate, "day"), endDate = this.endDate;
    for (let date2 = startDate; date2 < endDate; date2 = DateHelper.add(date2, 1, "day")) {
      dates.push(date2);
    }
    return dates;
  }
  get startDateMS() {
    var _a5;
    return (_a5 = this.batching && this.hasBatchedChange("startDate") ? this.get("startDate") : this.startDate) == null ? void 0 : _a5.getTime();
  }
  get endDateMS() {
    var _a5;
    return (_a5 = this.batching && this.hasBatchedChange("endDate") ? this.get("endDate") : this.endDate) == null ? void 0 : _a5.getTime();
  }
  /**
   * Returns the duration of this Event in milliseconds.
   * @readonly
   * @property {Number}
   * @category Scheduling
   */
  get durationMS() {
    return this.rawDurationMS;
  }
  /**
   * Returns the event raw duration in milliseconds.
   * Calculated as a simple distance between the event start and end dates in milliseconds.
   * And thus this value (in Scheduler Pro and Gantt) doesn't take into account non-working time
   * nor the project conversion rates.
   * @readonly
   * @property {Number}
   * @category Scheduling
   */
  get rawDurationMS() {
    const { endDateMS, startDateMS } = this;
    if (endDateMS && startDateMS) {
      return endDateMS - startDateMS;
    }
    return DateHelper.asMilliseconds(this.duration || 0, this.durationUnit);
  }
  /**
   * Returns true if record is a milestone.
   * @readonly
   * @property {Boolean}
   * @category Scheduling
   */
  get isMilestone() {
    return this.duration === 0;
  }
  inSetNormalize(field) {
    if (typeof field !== "string") {
      let { startDate, endDate, duration, durationUnit = this.durationUnit } = field;
      if (typeof startDate === "string") {
        startDate = this.getFieldDefinition("startDate").convert(startDate);
      }
      if (typeof endDate === "string") {
        endDate = this.getFieldDefinition("endDate").convert(endDate);
      }
      if ("duration" in field) {
        if (startDate && !endDate) {
          endDate = this.internalCalculateEndDate(startDate, duration, durationUnit);
        }
        if (!startDate && endDate) {
          startDate = this.internalCalculateStartDate(endDate, duration, durationUnit);
        }
      } else if (startDate && endDate) {
        duration = this.internalCalculateDuration(startDate, endDate, durationUnit);
      }
      const fieldOrClone = Object.isFrozen(field) ? ObjectHelper.clone(field) : field;
      startDate && (fieldOrClone.startDate = startDate);
      endDate && (fieldOrClone.endDate = endDate);
      duration != null && (fieldOrClone.duration = duration);
      return fieldOrClone;
    }
  }
  fieldToKeys(field, value2) {
    var _a5, _b2;
    const me = this, result = super.fieldToKeys(field, value2);
    if (result.fullDuration) {
      const { magnitude, unit } = result.fullDuration;
      result.duration = magnitude;
      result.durationUnit = unit;
    }
    if (!me.isEventModel && !me.isTaskModel) {
      if (("duration" in result || result.durationUnit) && !(result.startDate && result.endDate)) {
        Object.assign(
          result,
          me.updateDatesFromDuration(
            (_a5 = result.duration) != null ? _a5 : me.duration,
            (_b2 = result.durationUnit) != null ? _b2 : me.durationUnit,
            result.startDate,
            result.endDate
          )
        );
      }
    }
    return result;
  }
  inSet(field, value2, silent, fromRelationUpdate, skipAccessors, validOnly, triggerBeforeUpdate) {
    var _a5;
    const me = this;
    if (!skipAccessors) {
      field = me.inSetNormalize(field) || field;
    }
    const result = super.inSet(field, value2, silent, fromRelationUpdate, skipAccessors, validOnly, triggerBeforeUpdate);
    if (!((_a5 = me.project) == null ? void 0 : _a5.isWritingData) && field.startDate && field.endDate) {
      let invalid;
      if (field.startDate instanceof Date && field.endDate instanceof Date) {
        invalid = field.startDate > field.endDate;
      } else if (me.fieldMap.startDate.convert(field.startDate) > me.fieldMap.endDate.convert(field.endDate)) {
        invalid = true;
      }
      if (invalid) {
        console.error(`startDate > endDate for ${me.constructor.$name} record with id: ${me.id}`);
      }
    }
    return result;
  }
  // Cls requires special handling since it is converted to a DomClassList
  applyValue(useProp, key, value2, skipAccessors, field) {
    if (key === "cls") {
      this.updateInternalCls(value2);
    }
    super.applyValue(useProp, key, value2, skipAccessors, field);
  }
  //endregion
  //region Iteration
  /**
   * Iterates over the {@link #property-dates}
   * @param {Function} func The function to call for each date
   * @param {Object} thisObj `this` reference for the function
   * @category Scheduling
   */
  forEachDate(func, thisObj) {
    return this.dates.forEach(func.bind(thisObj));
  }
  //endregion
  /**
   * Checks if the range record has both start and end dates set and start <= end
   *
   * @property {Boolean}
   * @category Scheduling
   */
  get isScheduled() {
    const { startDateMS, endDateMS } = this;
    return endDateMS - startDateMS >= 0;
  }
  // Simple check if end date is greater than start date
  get isValid() {
    const { startDate, endDate } = this;
    return !startDate || !endDate || endDate - startDate >= 0;
  }
  /**
   * Shift the dates for the date range by the passed amount and unit
   * @param {Number} amount The amount to shift
   * @param {DurationUnit} unit The unit to shift by, see {@link Core.helper.DateHelper}
   * for more information on valid formats.
   */
  shift(amount, unit = this.durationUnit) {
    return this.setStartDate(DateHelper.add(this.startDate, amount, unit, true), true);
  }
  /**
   * Returns the WBS code of this model (e.g '2.1.3'). Only relevant when part of a tree store, as in the Gantt chart.
   * @property {String}
   * @category Parent & children
   */
  get wbsCode() {
    return this._wbsCode || this.indexPath.join(".");
  }
  set wbsCode(value2) {
    this._wbsCode = value2;
  }
  fullCopy() {
    return this.copy.apply(this, arguments);
  }
  intersects(timeSpan) {
    return this.intersectsRange(timeSpan.startDate, timeSpan.endDate);
  }
  intersectsRange(start, end) {
    const myStart = this.startDate, myEnd = this.endDate;
    return myStart && myEnd && DateHelper.intersectSpans(myStart, myEnd, start, end);
  }
  /**
   * Splits this event into two pieces at the desired position.
   *
   * @param {Number|String} splitPoint The duration point at which to split this event.
   *
   * If less then `1`, this indicates the relative position at which it will be split.
   * 0.5 means cut it in half.
   *
   * If greater than `1`, this indicates the new duration in the current duration units of this event before the split.
   *
   * If this is a string, it will be a duration description as described in
   * {@link Core.helper.DateHelper#function-parseDuration-static}, for example `'15 min'`
   *
   * @returns {Scheduler.model.TimeSpan} The newly created split section of the timespan
   * @category Scheduling
   */
  split(splitPoint = 0.5) {
    const me = this, clone = me.copy(), {
      fullDuration,
      eventStore,
      assignmentStore
    } = me, oldDuration = new Duration(fullDuration), cloneDuration = new Duration(fullDuration);
    let ownNewDuration, unitsChanged;
    if (typeof splitPoint === "string") {
      ownNewDuration = new Duration(splitPoint);
      if (ownNewDuration.unit === oldDuration.unit) {
        cloneDuration.magnitude -= ownNewDuration.magnitude;
      } else {
        cloneDuration.magnitude = DateHelper.as(ownNewDuration.unit, oldDuration) - ownNewDuration.magnitude;
        cloneDuration.unit = ownNewDuration.unit;
        unitsChanged = true;
      }
    } else {
      ownNewDuration = new Duration(splitPoint > 1 ? splitPoint : me.duration * splitPoint, me.durationUnit);
      cloneDuration.magnitude -= ownNewDuration.magnitude;
    }
    clone.startDate = DateHelper.add(me.startDate, ownNewDuration.magnitude, ownNewDuration.unit);
    if (unitsChanged) {
      clone.fullDuration = cloneDuration;
      me.fullDuration = ownNewDuration;
    } else {
      clone.duration = cloneDuration.magnitude;
      me.duration = ownNewDuration.magnitude;
    }
    if (eventStore) {
      eventStore.add(clone);
      if (assignmentStore && !eventStore.usesSingleAssignment) {
        assignmentStore.add(
          me.assignments.map((assignment) => {
            const clonedData = Object.assign({}, assignment.data, {
              eventId: clone.id,
              // From engine
              event: null,
              resource: null
            });
            delete clonedData.id;
            return clonedData;
          })
        );
      }
    }
    return clone;
  }
  toICSString(icsEventConfig = {}) {
    if (!this.isScheduled) {
      return "";
    }
    const {
      startDate,
      endDate
    } = this, timestamp = icsEventConfig.DTSTAMP || DateHelper.format(/* @__PURE__ */ new Date(), "uu");
    delete icsEventConfig.DTSTAMP;
    let startEnd = {};
    if (this.allDay) {
      startEnd = {
        "DTSTART;VALUE=DATE": DateHelper.format(startDate, "u"),
        "DTEND;VALUE=DATE": DateHelper.format(endDate, "u")
      };
    } else {
      startEnd = {
        DTSTART: DateHelper.format(startDate, "uu"),
        DTEND: DateHelper.format(endDate, "uu")
      };
    }
    const version = VersionHelper.scheduler && VersionHelper.getVersion("scheduler") || VersionHelper.calendar && VersionHelper.getVersion("calendar") || "", icsWrapConfig = {
      BEGIN: "VCALENDAR",
      VERSION: "2.0",
      CALSCALE: "GREGORIAN",
      PRODID: `-//Bryntum AB//Bryntum Scheduler ${version} //EN`,
      END: "VCALENDAR"
    }, eventConfig = {
      BEGIN: "VEVENT",
      UID: this.id + "@bryntum.com",
      CLASS: "PUBLIC",
      SUMMARY: this.name,
      DTSTAMP: timestamp,
      ...startEnd,
      ...this.recurrenceRule ? { RRULE: this.recurrenceRule } : {},
      ...icsEventConfig,
      END: "VEVENT"
    }, icsItems = Object.keys(icsWrapConfig).map((key) => `${key}:${icsWrapConfig[key]}`), eventItems = Object.keys(eventConfig).map((key) => `${key}:${eventConfig[key]}`);
    icsItems.splice(icsItems.length - 1, 0, ...eventItems);
    return icsItems.join("\n");
  }
  /**
   * Triggers a download of this time span in ICS format (for import in Outlook etc.)
   *
   * ```javascript
   * timeSpan.downloadAsICS({
   *      LOCATION : timeSpan.location
   *  });
   * ```
   * @param {Object<String,String>} [icsEventConfig] A config object with properties to be added in to `BEGIN:VEVENT`
   * section of the exported event.
   * @category Misc
   */
  exportToICS(icsEventConfig) {
    if (this.isScheduled) {
      const blob = new Blob([this.toICSString(icsEventConfig)], { type: "text/calendar" });
      BrowserHelper.downloadBlob(blob, (this.name || "Event") + ".ics");
    }
  }
  isFieldModified(fieldName) {
    if (fieldName === "fullDuration") {
      return super.isFieldModified("duration") || super.isFieldModified("durationUnit");
    }
    return super.isFieldModified(fieldName);
  }
};
__publicField(TimeSpan, "$name", "TimeSpan");
//region Field definitions
__publicField(TimeSpan, "fields", [
  /**
   * The start date of a time span (or Event / Task).
   *
   * Uses {@link Core/helper/DateHelper#property-defaultFormat-static DateHelper.defaultFormat} to convert a
   * supplied string to a Date. To specify another format, either change that setting or subclass TimeSpan and
   * change the dateFormat for this field.
   *
   * Note that the field always returns a `Date`.
   *
   * @field {Date} startDate
   * @accepts {String|Date}
   * @category Scheduling
   */
  {
    name: "startDate",
    type: "date"
  },
  /**
   * The end date of a time span (or Event / Task).
   *
   * Uses {@link Core/helper/DateHelper#property-defaultFormat-static DateHelper.defaultFormat} to convert a
   * supplied string to a Date. To specify another format, either change that setting or subclass TimeSpan and
   * change the dateFormat for this field.
   *
   * Note that the field always returns a `Date`.
   *
   * @field {Date} endDate
   * @accepts {String|Date}
   * @category Scheduling
   */
  {
    name: "endDate",
    type: "date"
  },
  /**
   * The numeric part of the timespan's duration (the number of units).
   * @field {Number} duration
   * @category Scheduling
   */
  {
    name: "duration",
    type: "number",
    allowNull: true,
    internal: true
  },
  /**
   * The unit part of the TimeSpan duration, defaults to "d" (days). Valid values are:
   *
   * - "millisecond" - Milliseconds
   * - "second" - Seconds
   * - "minute" - Minutes
   * - "hour" - Hours
   * - "day" - Days
   * - "week" - Weeks
   * - "month" - Months
   * - "quarter" - Quarters
   * - "year"- Years
   *
   * This field is readonly after creation, to change durationUnit use #setDuration().
   * @field {DurationUnit} durationUnit
   * @category Scheduling
   */
  {
    type: "durationunit",
    name: "durationUnit",
    defaultValue: "d",
    internal: true
  },
  /**
   * Calculated field which encapsulates the duration's magnitude and unit. This field will not be persisted,
   * setting it will update the {@link #field-duration} and {@link #field-durationUnit} fields.
   *
   * @field {DurationConfig|Core.data.Duration} fullDuration
   * @category Scheduling
   */
  {
    name: "fullDuration",
    persist: false,
    column: {
      type: "duration"
    },
    useProp: true
  },
  /**
   * An encapsulation of the CSS classes to add to the rendered time span element.
   *
   * Always returns a {@link Core.helper.util.DomClassList}, but may still be treated as a string. For
   * granular control of adding and removing individual classes, it is recommended to use the
   * {@link Core.helper.util.DomClassList} API.
   *
   * @field {Core.helper.util.DomClassList} cls
   * @accepts {Core.helper.util.DomClassList|String|String[]|Object}
   *
   * @category Styling
   */
  {
    name: "cls",
    defaultValue: "",
    internal: true
  },
  /**
   * CSS class specifying an icon to apply to the rendered time span element.
   * **Note**: In case event is a milestone, using `iconCls` with dependency feature might slightly decrease
   * performance because feature will refer to the DOM to get exact size of the element.
   * @field {String} iconCls
   * @category Styling
   */
  {
    name: "iconCls",
    internal: true
  },
  /**
   * A CSS style string (applied to `style.cssText`) or object (applied to `style`)
   * ```
   * record.style = 'color: red;font-weight: 800';
   * ```
   *
   * @field {String} style
   * @category Styling
   */
  {
    name: "style",
    type: "object",
    internal: true
  },
  /**
   * The name of the time span (or Event / Task)
   * @field {String} name
   * @category Common
   */
  {
    name: "name",
    type: "string",
    defaultValue: ""
  }
]);
TimeSpan._$name = "TimeSpan";

// ../Scheduler/lib/Scheduler/preset/ViewPreset.js
var ViewPreset = class extends Model {
  static get fields() {
    return [
      /**
       * The name of an existing view preset to extend
       * @field {String} base
       */
      { name: "base", type: "string" },
      /**
       * The name of the view preset
       * @field {String} name
       */
      { name: "name", type: "string" },
      /**
       * The height of the row in horizontal orientation
       * @field {Number} rowHeight
       */
      { name: "rowHeight" },
      /**
       * The width of the time tick column in horizontal orientation
       * @field {Number} tickWidth
       * @default
       */
      {
        name: "tickWidth",
        defaultValue: 50
      },
      /**
       * The height of the time tick column in vertical orientation
       * @field {Number} tickHeight
       * @default
       */
      {
        name: "tickHeight",
        defaultValue: 50
      },
      /**
       * Defines how dates will be formatted in tooltips etc
       * @field {String} displayDateFormat
       * @default
       */
      {
        name: "displayDateFormat",
        defaultValue: "HH:mm"
      },
      /**
       * The unit to shift when calling shiftNext/shiftPrevious to navigate in the chart.
       * Valid values are "millisecond", "second", "minute", "hour", "day", "week", "month", "quarter", "year".
       * @field {DurationUnit} shiftUnit
       * @default
       */
      {
        name: "shiftUnit",
        defaultValue: "hour"
      },
      /**
       * The amount to shift (in shiftUnits)
       * @field {Number} shiftIncrement
       * @default
       */
      {
        name: "shiftIncrement",
        defaultValue: 1
      },
      /**
       * The amount of time to show by default in a view (in the unit defined by {@link #field-mainUnit})
       * @field {Number} defaultSpan
       * @default
       */
      {
        name: "defaultSpan",
        defaultValue: 12
      },
      /**
       * Initially set to a unit. Defaults to the unit defined by the middle header.
       * @field {DurationUnit} mainUnit
       */
      {
        name: "mainUnit"
      },
      /**
       * Note: Currently, this field only applies when changing viewPreset with the {@link Scheduler.widget.ViewPresetCombo}.
       *
       * Set to a number and that amount of {@link #field-mainUnit} will be added to the startDate. For example: A
       * start value of `5` together with the mainUnit `hours` will add 5 hours to the startDate. This can achieve
       * a "day view" that starts 5 AM.
       *
       * Set to a string unit (for example week, day, month) and the startDate will be the start of that unit
       * calculated from current startDate. A start value of `week` will result in a startDate in the first day of
       * the week.
       *
       * If set to a number or not set at all, the startDate will be calculated at the beginning of current
       * mainUnit.
       * @field {Number|String} start
       */
      {
        name: "start"
      },
      /**
       * An object containing a unit identifier and an increment variable. This value means minimal task duration
       * you can create using UI. For example when you drag create a task or drag & drop a task, if increment is 5
       * and unit is 'minute' that means that you can create a 5-minute-long task, or move it 5 min
       * forward/backward. This config maps to scheduler's
       * {@link Scheduler.view.mixin.TimelineDateMapper#property-timeResolution} config.
       *
       * ```javascript
       * timeResolution : {
       *   unit      : 'minute',  //Valid values are "millisecond", "second", "minute", "hour", "day", "week", "month", "quarter", "year".
       *   increment : 5
       * }
       * ```
       *
       * @field {ViewPresetTimeResolution} timeResolution
       */
      "timeResolution",
      /**
       * An array containing one or more {@link #typedef-ViewPresetHeaderRow} config objects, each of
       * which defines a level of headers for the scheduler.
       * The `main` unit will be the last header's unit, but this can be changed using the
       * {@link #field-mainHeaderLevel} field.
       * @field {ViewPresetHeaderRow[]} headers
       * @typings {Partial<ViewPresetHeaderRow>[]}
       */
      "headers",
      /**
       * Index of the {@link #field-headers} array to define which header level is the `main` header.
       * Defaults to the bottom header.
       * @field {Number} mainHeaderLevel
       */
      "mainHeaderLevel",
      /**
       * Index of a header level in the {@link #field-headers} array for which column lines are drawn. See
       * {@link Scheduler.feature.ColumnLines}.
       * Defaults to the bottom header.
       * @field {Number} columnLinesFor
       */
      "columnLinesFor"
    ];
  }
  construct() {
    super.construct(...arguments);
    this.normalizeUnits();
  }
  generateId(owner) {
    const me = this, {
      headers
    } = me, parts = [];
    let result = Object.getPrototypeOf(me.data).id;
    if (!result) {
      for (let { length } = headers, i = length - 1; i >= 0; i--) {
        const { unit, increment } = headers[i], multiple = increment > 1;
        parts.push(`${multiple ? increment : ""}${i ? unit : StringHelper.capitalize(unit)}${multiple ? "s" : ""}`);
      }
      result = parts.join("And");
    }
    if (owner.count && owner.includes(result)) {
      result += `-${me.tickWidth}by${me.tickHeight || me.tickWidth}`;
      if (owner.includes(result)) {
        result += `-${me.bottomHeader.increment}`;
        if (owner.includes(result)) {
          result = IdHelper.generateId(`${result}-`);
        }
      }
    }
    return result;
  }
  normalizeUnits() {
    const me = this, { timeResolution, headers, shiftUnit } = me;
    if (headers) {
      for (let i = 0, { length } = headers; i < length; i++) {
        const header = headers[i];
        header.unit = DateHelper.normalizeUnit(header.unit);
        if (header.splitUnit) {
          header.splitUnit = DateHelper.normalizeUnit(header.splitUnit);
        }
        if (!("increment" in header)) {
          headers[i] = Object.assign({
            increment: 1
          }, header);
        }
      }
    }
    if (timeResolution) {
      timeResolution.unit = DateHelper.normalizeUnit(timeResolution.unit);
    }
    if (shiftUnit) {
      me.shiftUnit = DateHelper.normalizeUnit(shiftUnit);
    }
  }
  // Process legacy columnLines config into a headers array.
  static normalizeHeaderConfig(data) {
    const { headerConfig, columnLinesFor, mainHeaderLevel } = data, headers = data.headers = [];
    if (headerConfig.top) {
      if (columnLinesFor === "top") {
        data.columnLinesFor = 0;
      }
      if (mainHeaderLevel === "top") {
        data.mainHeaderLevel = 0;
      }
      headers[0] = headerConfig.top;
    }
    if (headerConfig.middle) {
      if (columnLinesFor === "middle") {
        data.columnLinesFor = headers.length;
      }
      if (mainHeaderLevel === "middle") {
        data.mainHeaderLevel = headers.length;
      }
      headers.push(headerConfig.middle);
    } else {
      throw new Error("ViewPreset.headerConfig must be configured with a middle");
    }
    if (headerConfig.bottom) {
      data.mainHeaderLevel = headers.length - 1;
      if (columnLinesFor == null) {
        data.columnLinesFor = headers.length - 1;
      } else if (columnLinesFor === "bottom") {
        data.columnLinesFor = headers.length;
      }
      if (mainHeaderLevel == null) {
        data.mainHeaderLevel = headers.length - 1;
      }
      if (mainHeaderLevel === "bottom") {
        data.mainHeaderLevel = headers.length;
      }
      headers.push(headerConfig.bottom);
    }
  }
  // These are read-only once configured.
  set() {
  }
  inSet() {
  }
  // Options is used to apply startDate, endDate etc. when changing to a new view preset
  set options(options) {
    this.data.options = options;
  }
  get options() {
    return this.data.options;
  }
  get columnLinesFor() {
    return "columnLinesFor" in this.data ? this.data.columnLinesFor : this.headers.length - 1;
  }
  get tickSize() {
    return this._tickSize || this.tickWidth;
  }
  get tickWidth() {
    return "tickWidth" in this.data ? this.data.tickWidth : 50;
  }
  get tickHeight() {
    return "tickHeight" in this.data ? this.data.tickHeight : 50;
  }
  get headerConfig() {
    if (this.data.headerConfig) {
      return this.data.headerConfig;
    }
    const result = {}, { headers } = this, { length } = headers;
    switch (length) {
      case 1:
        result.middle = headers[0];
        break;
      case 2:
        if (this.mainHeaderLevel === 0) {
          result.middle = headers[0];
          result.bottom = headers[1];
        } else {
          result.top = headers[0];
          result.middle = headers[1];
        }
        break;
      case 3:
        result.top = headers[0];
        result.middle = headers[1];
        result.bottom = headers[2];
        break;
      default:
        throw new Error("headerConfig object not supported for >3 header levels");
    }
    return result;
  }
  set mainHeaderLevel(mainHeaderLevel) {
    this.data.mainHeaderLevel = mainHeaderLevel;
  }
  get mainHeaderLevel() {
    if ("mainHeaderLevel" in this.data) {
      return this.data.mainHeaderLevel;
    }
    if (this.data.headers.length === 3) {
      return 1;
    }
    return this.headers.length - 1;
  }
  get mainHeader() {
    return this.headers[this.mainHeaderLevel];
  }
  get topHeader() {
    return this.headers[0];
  }
  get topUnit() {
    return this.topHeader.unit;
  }
  get topIncrement() {
    return this.topHeader.increment;
  }
  get bottomHeader() {
    return this.headers[this.headers.length - 1];
  }
  get leafUnit() {
    return this.bottomHeader.unit;
  }
  get leafIncrement() {
    return this.bottomHeader.increment;
  }
  get mainUnit() {
    if ("mainUnit" in this.data) {
      return this.data.mainUnit;
    }
    return this.mainHeader.unit;
  }
  get msPerPixel() {
    const { bottomHeader } = this;
    return Math.round(DateHelper.asMilliseconds(bottomHeader.increment || 1, bottomHeader.unit) / this.tickWidth);
  }
  get isValid() {
    const me = this;
    let valid = true;
    for (const header of me.headers) {
      valid = valid && Boolean(DateHelper.normalizeUnit(header.unit));
    }
    if (me.timeResolution) {
      valid = valid && DateHelper.normalizeUnit(me.timeResolution.unit);
    }
    if (me.shiftUnit) {
      valid = valid && DateHelper.normalizeUnit(me.shiftUnit);
    }
    return valid;
  }
};
__publicField(ViewPreset, "$name", "ViewPreset");
ViewPreset._$name = "ViewPreset";

// ../Scheduler/lib/Scheduler/localization/En.js
var locale4 = {
  localeName: "En",
  localeDesc: "English (US)",
  localeCode: "en-US",
  localeRtl: false,
  Object: {
    newEvent: "New event",
    information: "Information",
    showDetails: "Show details"
  },
  ResourceInfoColumn: {
    eventCountText: (data) => data + " event" + (data !== 1 ? "s" : "")
  },
  ResourceCollapseColumn: {
    toggleResourceLayout: "Toggle resource layout column. SPACE for context menu."
  },
  Dependencies: {
    from: "From",
    to: "To",
    valid: "Valid",
    invalid: "Invalid"
  },
  DependencyType: {
    SS: "SS",
    SF: "SF",
    FS: "FS",
    FF: "FF",
    StartToStart: "Start-to-Start",
    StartToEnd: "Start-to-Finish",
    EndToStart: "Finish-to-Start",
    EndToEnd: "Finish-to-Finish",
    short: [
      "SS",
      "SF",
      "FS",
      "FF"
    ],
    long: [
      "Start-to-Start",
      "Start-to-Finish",
      "Finish-to-Start",
      "Finish-to-Finish"
    ]
  },
  DependencyEdit: {
    From: "From",
    To: "To",
    Type: "Type",
    Lag: "Lag",
    "Edit dependency": "Edit dependency",
    Save: "Save",
    Delete: "Delete",
    Cancel: "Cancel",
    StartToStart: "Start to Start",
    StartToEnd: "Start to End",
    EndToStart: "End to Start",
    EndToEnd: "End to End"
  },
  EventEdit: {
    Name: "Name",
    Resource: "Resource",
    Start: "Start",
    startTime: "Start time",
    End: "End",
    endTime: "End time",
    Save: "Save",
    Delete: "Delete",
    Cancel: "Cancel",
    "Edit event": "Edit",
    Repeat: "Repeat"
  },
  EventDrag: {
    eventOverlapsExisting: "Event overlaps existing event for this resource",
    noDropOutsideTimeline: "Event may not be dropped completely outside the timeline"
  },
  SchedulerBase: {
    "Add event": "Add event",
    "Delete event": "Delete",
    "Unassign event": "Unassign",
    color: "Color",
    eventAriaLabel: (eventRecord, startDate) => `${eventRecord.name}, starts at ${startDate} ${eventRecord.duration > 0 ? `for ${eventRecord.fullDuration.toString(false)}` : ""}`
  },
  TimeAxisHeaderMenu: {
    pickZoomLevel: "Zoom",
    activeDateRange: "Date range",
    startText: "Start date",
    endText: "End date",
    todayText: "Today"
  },
  TimeAxisColumn: {
    ariaLabel: (startDate, endDate) => `Time axis showing ${startDate} to ${endDate}`
  },
  EventCopyPaste: {
    copyEvent: "Copy",
    cutEvent: "Cut",
    pasteEvent: "Paste"
  },
  EventFilter: {
    filterEvents: "Filter tasks",
    byName: "By name"
  },
  TimeRanges: {
    showCurrentTimeLine: "Show current timeline"
  },
  PresetManager: {
    secondAndMinute: {
      displayDateFormat: "ll LTS",
      name: "Seconds"
    },
    minuteAndHour: {
      topDateFormat: "ddd MM/DD, hA",
      displayDateFormat: "ll LST"
    },
    hourAndDay: {
      topDateFormat: "ddd MM/DD",
      middleDateFormat: "LST",
      displayDateFormat: "ll LST",
      name: "Day"
    },
    day: {
      name: "Day/hours"
    },
    week: {
      name: "Week/hours"
    },
    dayAndWeek: {
      displayDateFormat: "ll LST",
      name: "Week/days"
    },
    dayAndMonth: {
      name: "Month"
    },
    weekAndDay: {
      displayDateFormat: "ll LST",
      name: "Week"
    },
    weekAndMonth: {
      name: "Weeks"
    },
    weekAndDayLetter: {
      name: "Weeks/weekdays"
    },
    weekDateAndMonth: {
      name: "Months/weeks"
    },
    monthAndYear: {
      name: "Months"
    },
    year: {
      name: "Years"
    },
    manyYears: {
      name: "Multiple years"
    }
  },
  RecurrenceConfirmationPopup: {
    "delete-title": "You are deleting an event",
    "delete-all-message": "Do you want to delete all occurrences of this event?",
    "delete-further-message": "Do you want to delete this and all future occurrences of this event, or only the selected occurrence?",
    "delete-only-this-message": "Do you want to delete this occurrence of this event?",
    "delete-further-btn-text": "Delete All Future Events",
    "delete-only-this-btn-text": "Delete Only This Event",
    "update-title": "You are changing a repeating event",
    "update-all-message": "Do you want to change all occurrences of this event?",
    "update-further-message": "Do you want to change only this occurrence of the event, or this and all future occurrences?",
    "update-only-this-message": "Do you want to change this occurrence of this event?",
    "update-further-btn-text": "All Future Events",
    "update-only-this-btn-text": "Only This Event",
    Yes: "Yes",
    Cancel: "Cancel",
    width: 600
  },
  RecurrenceLegend: {
    " and ": " and ",
    Daily: "Daily",
    "Weekly on {1}": ({ days: days2 }) => `Weekly on ${days2}`,
    "Monthly on {1}": ({ days: days2 }) => `Monthly on ${days2}`,
    "Yearly on {1} of {2}": ({ days: days2, months }) => `Yearly on ${days2} of ${months}`,
    "Every {0} days": ({ interval }) => `Every ${interval} days`,
    "Every {0} weeks on {1}": ({ interval, days: days2 }) => `Every ${interval} weeks on ${days2}`,
    "Every {0} months on {1}": ({ interval, days: days2 }) => `Every ${interval} months on ${days2}`,
    "Every {0} years on {1} of {2}": ({ interval, days: days2, months }) => `Every ${interval} years on ${days2} of ${months}`,
    position1: "the first",
    position2: "the second",
    position3: "the third",
    position4: "the fourth",
    position5: "the fifth",
    "position-1": "the last",
    day: "day",
    weekday: "weekday",
    "weekend day": "weekend day",
    daysFormat: ({ position, days: days2 }) => `${position} ${days2}`
  },
  RecurrenceEditor: {
    "Repeat event": "Repeat event",
    Cancel: "Cancel",
    Save: "Save",
    Frequency: "Frequency",
    Every: "Every",
    DAILYintervalUnit: "day(s)",
    WEEKLYintervalUnit: "week(s)",
    MONTHLYintervalUnit: "month(s)",
    YEARLYintervalUnit: "year(s)",
    Each: "Each",
    on: "On",
    the: "The",
    "On the": "On the",
    "End repeat": "End repeat",
    "time(s)": "time(s)",
    Days: "Days",
    Months: "Months"
  },
  RecurrenceDaysCombo: {
    day: "day",
    weekday: "weekday",
    "weekend day": "weekend day"
  },
  RecurrencePositionsCombo: {
    position1: "first",
    position2: "second",
    position3: "third",
    position4: "fourth",
    position5: "fifth",
    "position-1": "last"
  },
  RecurrenceStopConditionCombo: {
    Never: "Never",
    After: "After",
    "On date": "On date"
  },
  RecurrenceFrequencyCombo: {
    None: "No repeat",
    Daily: "Daily",
    Weekly: "Weekly",
    Monthly: "Monthly",
    Yearly: "Yearly"
  },
  RecurrenceCombo: {
    None: "None",
    Custom: "Custom..."
  },
  Summary: {
    "Summary for": (date2) => `Summary for ${date2}`
  },
  ScheduleRangeCombo: {
    completeview: "Complete schedule",
    currentview: "Visible schedule",
    daterange: "Date range",
    completedata: "Complete schedule (for all events)"
  },
  SchedulerExportDialog: {
    "Schedule range": "Schedule range",
    "Export from": "From",
    "Export to": "To"
  },
  ExcelExporter: {
    "No resource assigned": "No resource assigned"
  },
  CrudManagerView: {
    serverResponseLabel: "Server response:"
  },
  DurationColumn: {
    Duration: "Duration"
  }
};
var En_default3 = LocaleHelper.publishLocale(locale4);

// ../Scheduler/lib/Scheduler/preset/PresetStore.js
var PresetStore = class extends Localizable_default(Store) {
  static get defaultConfig() {
    return {
      useRawData: true,
      modelClass: ViewPreset,
      /**
       * Specifies the sort order of the presets in the store.
       * By default they are in zoomed out to zoomed in order. That is
       * presets which will create widest event bars to presets
       * which will produce narrowest event bars.
       *
       * Configure this as `-1` to reverse this order.
       * @config {Number}
       * @default
       */
      zoomOrder: 1
    };
  }
  set storage(storage) {
    super.storage = storage;
    this.storage.addSorter((lhs, rhs) => {
      const leftBottomHeader = lhs.bottomHeader, rightBottomHeader = rhs.bottomHeader;
      const order = rhs.msPerPixel - lhs.msPerPixel || DateHelper.compareUnits(leftBottomHeader.unit, rightBottomHeader.unit) || leftBottomHeader.increment - rightBottomHeader.increment;
      return order * this.zoomOrder;
    });
  }
  get storage() {
    return super.storage;
  }
  // So not to extend the ViewPreset class
  changeModelClass(classDef) {
    return classDef;
  }
  getById(id2) {
    return super.getById(id2) || !this.isPresetManager && globalThis.bryntum.PresetManager.getById(id2);
  }
  createRecord(data, ...args) {
    let result;
    if (data.isViewPreset) {
      return data;
    }
    if (typeof data === "string") {
      result = this.getById(data);
    } else if (typeof data === "number") {
      result = this.getAt(data);
    } else {
      if (data.base) {
        data = this.copyBaseValues(data);
      }
      return super.createRecord(data, ...args);
    }
    if (!result) {
      throw new Error(`ViewPreset ${data} does not exist`);
    }
    return result;
  }
  updateLocalization() {
    super.updateLocalization();
    const me = this;
    let presets = me.allRecords;
    if (me.isPresetManager) {
      presets = new Set(presets.concat(Object.values(me.basePresets)));
    }
    presets.forEach((preset) => {
      let localePreset = me.optionalL(`L{PresetManager.${preset.id}}`, null, true);
      if (typeof localePreset === "string" && preset.baseId) {
        localePreset = me.optionalL(`L{PresetManager.${preset.baseId}}`, null, true);
      }
      if (localePreset && typeof localePreset === "object") {
        if (!preset.originalDisplayDateFormat) {
          preset.originalDisplayDateFormat = preset.displayDateFormat;
        }
        if (preset.mainHeaderLevel === 0 && localePreset.topDateFormat) {
          localePreset.middleDateFormat = localePreset.middleDateFormat || localePreset.topDateFormat;
        }
        preset.setData("displayDateFormat", localePreset.displayDateFormat || preset.originalDisplayDateFormat);
        ["top", "middle", "bottom"].forEach((level) => {
          const levelConfig = preset.headerConfig[level], localeLevelDateFormat = localePreset[level + "DateFormat"];
          if (levelConfig) {
            if (!levelConfig.originalDateFormat && levelConfig.dateFormat) {
              levelConfig.originalDateFormat = levelConfig.dateFormat;
            }
            if (localeLevelDateFormat && levelConfig.renderer) {
              levelConfig.renderer = null;
            }
            const dateFormat = localeLevelDateFormat || levelConfig.originalDateFormat;
            if (dateFormat) {
              levelConfig.dateFormat = dateFormat;
            }
          }
        });
        if (localePreset.name) {
          if (!preset.unlocalizedName) {
            preset.unlocalizedName = preset.name;
          }
          preset.setData("name", localePreset.name);
        } else if (preset.unlocalizedName && preset.unlocalizedName !== preset.name) {
          preset.name = preset.unlocalizedName;
          preset.unlocalizedName = null;
        }
      }
    });
  }
  // This function is not meant to be called by any code other than Base#getCurrentConfig().
  // Preset config on Scheduler/Gantt expects array of presets and not store config
  getCurrentConfig(options) {
    return super.getCurrentConfig(options).data;
  }
  copyBaseValues(presetData) {
    let base = this.getById(presetData.base);
    if (!base) {
      throw new Error(`ViewPreset base '${presetData.base}' does not exist.`);
    }
    base = ObjectHelper.clone(base.data);
    delete base.id;
    if (presetData.name) {
      delete base.name;
    }
    return ObjectHelper.merge(base, presetData);
  }
  add(preset) {
    preset = Array.isArray(preset) ? preset : [preset];
    preset.forEach((preset2) => {
      if (preset2.isViewPreset && preset2.base) {
        preset2.data = this.copyBaseValues(preset2.originalData);
      }
    });
    return super.add(...arguments);
  }
};
__publicField(PresetStore, "$name", "PresetStore");
PresetStore._$name = "PresetStore";

// ../Scheduler/lib/Scheduler/preset/PresetManager.js
var PresetManager = class extends PresetStore {
  static get defaultConfig() {
    return {
      // To not break CSP demo
      preventSubClassingModel: true,
      basePresets: {
        secondAndMinute: {
          name: "Seconds",
          tickWidth: 30,
          // Time column width
          tickHeight: 40,
          displayDateFormat: "ll LTS",
          // Controls how dates will be displayed in tooltips etc
          shiftIncrement: 10,
          // Controls how much time to skip when calling shiftNext and shiftPrevious.
          shiftUnit: "minute",
          // Valid values are "millisecond", "second", "minute", "hour", "day", "week", "month", "quarter", "year".
          defaultSpan: 24,
          // By default, if no end date is supplied to a view it will show 24 hours
          timeResolution: {
            // Dates will be snapped to this resolution
            unit: "second",
            // Valid values are "millisecond", "second", "minute", "hour", "day", "week", "month", "quarter", "year".
            increment: 5
          },
          // This defines your header rows.
          // For each row you can define "unit", "increment", "dateFormat", "renderer", "align", and "thisObj"
          headers: [
            {
              unit: "minute",
              dateFormat: "lll"
            },
            {
              unit: "second",
              increment: 10,
              dateFormat: "ss"
            }
          ]
        },
        minuteAndHour: {
          name: "Minutes",
          tickWidth: 60,
          // Time column width
          tickHeight: 60,
          displayDateFormat: "ll LT",
          // Controls how dates will be displayed in tooltips etc
          shiftIncrement: 1,
          // Controls how much time to skip when calling shiftNext and shiftPrevious.
          shiftUnit: "hour",
          // Valid values are "MILLI", "SECOND", "minute", "HOUR", "DAY", "WEEK", "MONTH", "QUARTER", "YEAR".
          defaultSpan: 24,
          // By default, if no end date is supplied to a view it will show 24 hours
          timeResolution: {
            // Dates will be snapped to this resolution
            unit: "minute",
            // Valid values are "MILLI", "SECOND", "minute", "HOUR", "DAY", "WEEK", "MONTH", "QUARTER", "YEAR".
            increment: 15
          },
          headers: [
            {
              unit: "hour",
              dateFormat: "ddd MM/DD, hA"
            },
            {
              unit: "minute",
              increment: 30,
              dateFormat: "mm"
            }
          ]
        },
        hourAndDay: {
          name: "Day",
          tickWidth: 70,
          tickHeight: 40,
          displayDateFormat: "ll LT",
          shiftIncrement: 1,
          shiftUnit: "day",
          defaultSpan: 24,
          timeResolution: {
            unit: "minute",
            increment: 30
          },
          headers: [
            {
              unit: "day",
              dateFormat: "ddd DD/MM"
              //Mon 01/10
            },
            {
              unit: "hour",
              dateFormat: "LT"
            }
          ]
        },
        day: {
          name: "Day/hours",
          displayDateFormat: "LT",
          shiftIncrement: 1,
          shiftUnit: "day",
          defaultSpan: 1,
          timeResolution: {
            unit: "minute",
            increment: 30
          },
          mainHeaderLevel: 0,
          headers: [
            {
              unit: "day",
              dateFormat: "ddd DD/MM",
              // Mon 01/02
              splitUnit: "day"
            },
            {
              unit: "hour",
              renderer(value2) {
                return `
                                    <div class="b-sch-calendarcolumn-ct"><span class="b-sch-calendarcolumn-hours">${DateHelper.format(value2, "HH")}</span>
                                    <span class="b-sch-calendarcolumn-minutes">${DateHelper.format(value2, "mm")}</span></div>
                                `;
              }
            }
          ]
        },
        week: {
          name: "Week/hours",
          displayDateFormat: "LT",
          shiftIncrement: 1,
          shiftUnit: "week",
          defaultSpan: 24,
          timeResolution: {
            unit: "minute",
            increment: 30
          },
          mainHeaderLevel: 0,
          headers: [
            {
              unit: "week",
              dateFormat: "D d",
              splitUnit: "day"
            },
            {
              unit: "hour",
              dateFormat: "LT",
              // will be overridden by renderer
              renderer(value2) {
                return `
                                    <div class="sch-calendarcolumn-ct">
                                    <span class="sch-calendarcolumn-hours">${DateHelper.format(value2, "HH")}</span>
                                    <span class="sch-calendarcolumn-minutes">${DateHelper.format(value2, "mm")}</span>
                                    </div>
                                `;
              }
            }
          ]
        },
        dayAndWeek: {
          name: "Days & Weeks",
          tickWidth: 100,
          tickHeight: 80,
          displayDateFormat: "ll LT",
          shiftUnit: "day",
          shiftIncrement: 1,
          defaultSpan: 5,
          timeResolution: {
            unit: "hour",
            increment: 1
          },
          headers: [
            {
              unit: "week",
              renderer(start) {
                if (DateHelper.weekStartDay === 1) {
                  const [isoYear, isoWeek] = DateHelper.getWeekNumber(start), thursdayOfISOWeek = DateHelper.getDateFromISOWeek(isoYear, isoWeek, 4), monthName = DateHelper.format(thursdayOfISOWeek, "MMM");
                  return `${DateHelper.getShortNameOfUnit("week")}.${String(isoWeek).padStart(2, "0")} ${monthName} ${isoYear}`;
                } else {
                  return DateHelper.getShortNameOfUnit("week") + "." + DateHelper.format(start, "WW MMM YYYY");
                }
              }
            },
            {
              unit: "day",
              dateFormat: "dd DD"
            }
          ]
        },
        // dayAndMonth : {
        //     name              : 'Days & Months',
        //     tickWidth         : 100,
        //     tickHeight        : 80,
        //     displayDateFormat : 'll LT',
        //     shiftUnit         : 'day',
        //     shiftIncrement    : 1,
        //     defaultSpan       : 5,
        //     timeResolution    : {
        //         unit      : 'hour',
        //         increment : 1
        //     },
        //     headers : [
        //         {
        //             unit       : 'month',
        //             dateFormat : 'MMMM YYYY',
        //             align      : 'start'
        //         },
        //         {
        //             unit       : 'day',
        //             dateFormat : 'dd DD'
        //         }
        //     ]
        // },
        dayAndMonth: {
          name: "Month",
          tickWidth: 100,
          tickHeight: 80,
          displayDateFormat: "ll LT",
          shiftUnit: "month",
          shiftIncrement: 1,
          defaultSpan: 1,
          mainUnit: "month",
          timeResolution: {
            unit: "hour",
            increment: 1
          },
          headers: [
            {
              unit: "month",
              dateFormat: "MMMM YYYY"
            },
            {
              unit: "day",
              dateFormat: "DD"
            }
          ]
        },
        weekAndDay: {
          name: "Week",
          tickWidth: 100,
          tickHeight: 80,
          displayDateFormat: "ll hh:mm A",
          shiftUnit: "week",
          shiftIncrement: 1,
          defaultSpan: 1,
          timeResolution: {
            unit: "day",
            increment: 1
          },
          mainHeaderLevel: 0,
          headers: [
            {
              unit: "week",
              dateFormat: "YYYY MMMM DD"
              // 2017 January 01
            },
            {
              unit: "day",
              increment: 1,
              dateFormat: "DD MMM"
            }
          ]
        },
        weekAndMonth: {
          name: "Weeks",
          tickWidth: 100,
          tickHeight: 105,
          displayDateFormat: "ll",
          shiftUnit: "week",
          shiftIncrement: 5,
          defaultSpan: 6,
          timeResolution: {
            unit: "day",
            increment: 1
          },
          headers: [
            {
              unit: "month",
              dateFormat: "MMM YYYY"
              //Jan 2017
            },
            {
              unit: "week",
              dateFormat: "DD MMM"
            }
          ]
        },
        weekAndDayLetter: {
          name: "Weeks/weekdays",
          tickWidth: 20,
          tickHeight: 50,
          displayDateFormat: "ll",
          shiftUnit: "week",
          shiftIncrement: 1,
          defaultSpan: 10,
          timeResolution: {
            unit: "day",
            increment: 1
          },
          mainHeaderLevel: 0,
          headers: [
            {
              unit: "week",
              dateFormat: "ddd DD MMM YYYY",
              verticalColumnWidth: 115
            },
            {
              unit: "day",
              dateFormat: "d1",
              verticalColumnWidth: 25
            }
          ]
        },
        weekDateAndMonth: {
          name: "Months/weeks",
          tickWidth: 30,
          tickHeight: 40,
          displayDateFormat: "ll",
          shiftUnit: "week",
          shiftIncrement: 1,
          defaultSpan: 10,
          timeResolution: {
            unit: "day",
            increment: 1
          },
          headers: [
            {
              unit: "month",
              dateFormat: "YYYY MMMM"
            },
            {
              unit: "week",
              dateFormat: "DD"
            }
          ]
        },
        monthAndYear: {
          name: "Months",
          tickWidth: 110,
          tickHeight: 110,
          displayDateFormat: "ll",
          shiftIncrement: 3,
          shiftUnit: "month",
          defaultSpan: 12,
          timeResolution: {
            unit: "day",
            increment: 1
          },
          headers: [
            {
              unit: "year",
              dateFormat: "YYYY"
              //2017
            },
            {
              unit: "month",
              dateFormat: "MMM YYYY"
              //Jan 2017
            }
          ]
        },
        year: {
          name: "Years",
          tickWidth: 100,
          tickHeight: 100,
          resourceColumnWidth: 100,
          displayDateFormat: "ll",
          shiftUnit: "year",
          shiftIncrement: 1,
          defaultSpan: 1,
          mainHeaderLevel: 0,
          timeResolution: {
            unit: "month",
            increment: 1
          },
          headers: [
            {
              unit: "year",
              dateFormat: "YYYY"
            },
            {
              unit: "quarter",
              renderer(start, end, cfg) {
                return DateHelper.getShortNameOfUnit("quarter").toUpperCase() + (Math.floor(start.getMonth() / 3) + 1);
              }
            }
          ]
        },
        manyYears: {
          name: "Multiple years",
          tickWidth: 40,
          tickHeight: 50,
          displayDateFormat: "ll",
          shiftUnit: "year",
          shiftIncrement: 1,
          defaultSpan: 10,
          timeResolution: {
            unit: "year",
            increment: 1
          },
          mainHeaderLevel: 0,
          headers: [
            {
              unit: "year",
              increment: 5,
              renderer: (start, end) => start.getFullYear() + " - " + end.getFullYear()
            },
            {
              unit: "year",
              dateFormat: "YY",
              increment: 1
            }
          ]
        }
      },
      // This is a list of bryntum-supplied preset adjustments used to create the Scheduler's
      // default initial set of ViewPresets.
      defaultPresets: [
        // Years over years
        "manyYears",
        { width: 80, increment: 1, resolution: 1, base: "manyYears", resolutionUnit: "YEAR" },
        // Years over quarters
        "year",
        { width: 30, increment: 1, resolution: 1, base: "year", resolutionUnit: "MONTH" },
        { width: 50, increment: 1, resolution: 1, base: "year", resolutionUnit: "MONTH" },
        { width: 200, increment: 1, resolution: 1, base: "year", resolutionUnit: "MONTH" },
        // Years over months
        "monthAndYear",
        // Months over weeks
        "weekDateAndMonth",
        // Months over weeks
        "weekAndMonth",
        // Months over weeks
        "weekAndDayLetter",
        // Months over days
        "dayAndMonth",
        // Weeks over days
        "weekAndDay",
        { width: 54, increment: 1, resolution: 1, base: "weekAndDay", resolutionUnit: "HOUR" },
        // Days over hours
        "hourAndDay",
        { width: 64, increment: 6, resolution: 30, base: "hourAndDay", resolutionUnit: "MINUTE" },
        { width: 100, increment: 6, resolution: 30, base: "hourAndDay", resolutionUnit: "MINUTE" },
        { width: 64, increment: 2, resolution: 30, base: "hourAndDay", resolutionUnit: "MINUTE" },
        // Hours over minutes
        "minuteAndHour",
        { width: 60, increment: 15, resolution: 5, base: "minuteAndHour" },
        { width: 130, increment: 15, resolution: 5, base: "minuteAndHour" },
        { width: 60, increment: 5, resolution: 5, base: "minuteAndHour" },
        { width: 100, increment: 5, resolution: 5, base: "minuteAndHour" },
        // Minutes over seconds
        "secondAndMinute",
        { width: 60, increment: 10, resolution: 5, base: "secondAndMinute" },
        { width: 130, increment: 5, resolution: 5, base: "secondAndMinute" }
      ],
      internalListeners: {
        locale: "updateLocalization"
      }
    };
  }
  set basePresets(basePresets) {
    const presetCache = this._basePresets = {};
    this.getConfig("modelClass");
    for (const id2 in basePresets) {
      basePresets[id2].id = id2;
      presetCache[id2] = this.createRecord(basePresets[id2]);
    }
  }
  get basePresets() {
    return this._basePresets;
  }
  set defaultPresets(defaultPresets) {
    for (let i = 0, { length } = defaultPresets; i < length; i++) {
      const presetAdjustment = defaultPresets[i], isBase = typeof presetAdjustment === "string", baseType = isBase ? presetAdjustment : presetAdjustment.base;
      let preset;
      if (isBase) {
        preset = this.basePresets[baseType];
      } else {
        const config = Object.assign(/* @__PURE__ */ Object.create({ id: baseType }), ObjectHelper.clone(this.basePresets[baseType].data)), { timeResolution } = config, bottomHeader = config.headers[config.headers.length - 1];
        config.id = void 0;
        if ("width" in presetAdjustment) {
          config.tickWidth = presetAdjustment.width;
        }
        if ("height" in presetAdjustment) {
          config.tickHeight = presetAdjustment.height;
        }
        if ("increment" in presetAdjustment) {
          bottomHeader.increment = presetAdjustment.increment;
        }
        if ("resolution" in presetAdjustment) {
          timeResolution.increment = presetAdjustment.resolution;
        }
        if ("resolutionUnit" in presetAdjustment) {
          timeResolution.unit = DateHelper.getUnitByName(presetAdjustment.resolutionUnit);
        }
        preset = this.createRecord(config);
        preset.baseId = baseType;
      }
      this.add(preset);
    }
  }
  getById(id2) {
    return super.getById(id2) || this.basePresets[id2];
  }
  /**
   * Registers a new view preset base to be used by any scheduler grid or tree on the page.
   * @param {String} id The unique identifier for this preset
   * @param {ViewPresetConfig} config The configuration properties of the view preset (see
   * {@link Scheduler.preset.ViewPreset} for more information)
   * @returns {Scheduler.preset.ViewPreset} A new ViewPreset based upon the passed configuration.
   */
  registerPreset(id2, config) {
    const preset = this.createRecord(Object.assign({
      id: id2
    }, config)), existingDuplicate = this.find((p) => p.equals(preset));
    if (existingDuplicate) {
      return existingDuplicate;
    }
    if (preset.isValid) {
      this.add(preset);
    } else {
      throw new Error("Invalid preset, please check your configuration");
    }
    return preset;
  }
  getPreset(preset) {
    if (typeof preset === "number") {
      preset = this.getAt(preset);
    }
    if (typeof preset === "string") {
      preset = this.getById(preset);
    } else if (!(preset instanceof ViewPreset)) {
      preset = this.createRecord(preset);
    }
    return preset;
  }
  /**
   * Applies preset customizations or fetches a preset view preset using its name.
   * @param {String|ViewPresetConfig} presetOrId Id of a predefined preset or a preset config object
   * @returns {Scheduler.preset.ViewPreset} Resulting ViewPreset instance
   */
  normalizePreset(preset) {
    const me = this;
    if (!(preset instanceof ViewPreset)) {
      if (typeof preset === "string") {
        preset = me.getPreset(preset);
        if (!preset) {
          throw new Error("You must define a valid view preset. See PresetManager for reference");
        }
      } else if (typeof preset === "object") {
        if (preset.base) {
          const base = this.getById(preset.base);
          if (!base) {
            throw new Error(`ViewPreset base '${preset.base}' does not exist`);
          }
          preset = ObjectHelper.merge(ObjectHelper.clone(base.data), preset);
        }
        if (preset.id) {
          preset = me.createRecord(preset);
        } else {
          preset = me.createRecord(ObjectHelper.assign({}, preset));
          preset.id = preset.generateId(preset);
        }
      }
    }
    return preset;
  }
  /**
   * Deletes a view preset
   * @param {String} id The id of the preset, or the preset instance.
   */
  deletePreset(presetOrId) {
    if (typeof presetOrId === "string") {
      presetOrId = this.getById(presetOrId);
    } else if (typeof presetOrId === "number") {
      presetOrId = this.getAt(presetOrId);
    }
    if (presetOrId) {
      this.remove(presetOrId);
      delete this.basePresets[presetOrId.id];
    }
  }
};
__publicField(PresetManager, "$name", "PresetManager");
var pm = new PresetManager();
globalThis.bryntum.PresetManager = pm;

// ../Scheduler/lib/Scheduler/data/TimeAxis.js
var Tick = class extends TimeSpan {
  // Only getters on purpose, we do not support manipulating ticks
  get startDate() {
    return this.data.startDate;
  }
  get endDate() {
    return this.data.endDate;
  }
};
var TimeAxis = class extends Store {
  //region Events
  /**
   * Fires before the timeaxis is about to be reconfigured (e.g. new start/end date or unit/increment). Return `false`
   * to abort the operation.
   * @event beforeReconfigure
   * @param {Scheduler.data.TimeAxis} source The time axis instance
   * @param {Date} startDate The new time axis start date
   * @param {Date} endDate The new time axis end date
   * @preventable
   */
  /**
   * Event that is triggered when we end reconfiguring and everything UI-related should be done
   * @event endReconfigure
   * @private
   */
  /**
   * Fires when the timeaxis has been reconfigured (e.g. new start/end date or unit/increment)
   * @event reconfigure
   * @param {Scheduler.data.TimeAxis} source The time axis instance
   */
  /**
   * Fires if all the ticks in the timeaxis are filtered out. After firing the filter is temporarily disabled to
   * return the time axis to a valid state. A disabled filter will be re-enabled the next time ticks are regenerated
   * @event invalidFilter
   * @param {Scheduler.data.TimeAxis} source The time axis instance
   */
  //endregion
  //region Default config
  /**
   * @hideconfigs lazyLoad
   */
  /**
   * @hideproperties lazyLoad
   */
  /**
   * @hideevents lazyLoadStarted, lazyLoadEnded
   */
  /**
   * @hidefunctions requestData
   */
  static get defaultConfig() {
    return {
      modelClass: Tick,
      /**
       * Set to `false` if the timeline is not continuous, e.g. the next timespan does not start where the previous ended (for example skipping weekends etc).
       * @config {Boolean}
       * @default
       */
      continuous: true,
      originalContinuous: null,
      /**
       * Include only certain hours or days in the time axis (makes it `continuous : false`). Accepts and object
       * with `day` and `hour` properties:
       * ```
       * const scheduler = new Scheduler({
       *     timeAxis : {
       *         include : {
       *              // Do not display hours after 17 or before 9 (only display 9 - 17). The `to´ value is not
       *              // included in the time axis
       *              hour : {
       *                  from : 9,
       *                  to   : 17
       *              },
       *              // Do not display sunday or saturday
       *              day : [0, 6]
       *         }
       *     }
       * }
       * ```
       * In most cases we recommend that you use Scheduler's workingTime config instead. It is easier to use and
       * makes sure all parts of the Scheduler gets updated.
       * @config {Object}
       */
      include: null,
      /**
       * Automatically adjust the timespan when generating ticks with {@link #property-generateTicks} according to
       * the `viewPreset` configuration. Setting this to false may lead to shifting time/date of ticks.
       * @config {Boolean}
       * @default
       */
      autoAdjust: true,
      //isConfigured : false,
      // in case of `autoAdjust : false`, the 1st and last ticks can be truncated, containing only part of the normal tick
      // these dates will contain adjusted start/end (like if the tick has not been truncated)
      adjustedStart: null,
      adjustedEnd: null,
      // the visible position in the first tick, can actually be > 1 because the adjustment is done by the `mainUnit`
      visibleTickStart: null,
      // the visible position in the first tick, is always ticks count - 1 < value <= ticks count, in case of autoAdjust, always = ticks count
      visibleTickEnd: null,
      tickCache: {},
      viewPreset: null,
      maxTraverseTries: 100,
      useRawData: {
        disableDuplicateIdCheck: true,
        disableDefaultValue: true,
        disableTypeConversion: true
      },
      // we prevent using endless tick amount for performance reasons. We will recommend to reduce date range as an erorr in console
      maxAllowedTickCount: 1e4
    };
  }
  static get configurable() {
    return {
      /**
       * Method generating the ticks for this time axis. Should return a **non-empty** array of ticks. Each tick
       * is an object of the following structure:
       * ```
       * {
       *    startDate : ..., // start date
       *    endDate   : ...  // end date
       * }
       * ```
       * To see it in action please check out our [TimeAxis](https://bryntum.com/products/scheduler/examples/timeaxis/)
       * example and navigate to "Compressed non-working time" tab.
       *
       * @param {Date} axisStartDate The start date of the interval
       * @param {Date} axisEndDate The end date of the interval
       * @param {DurationUnit} unit The unit of the time axis
       * @param {Number} increment The increment for the unit specified.
       * @returns {TimeSpanConfig[]} ticks The ticks representing the time axis
       * @config {Function}
       */
      generateTicks: null,
      unit: null,
      increment: null,
      resolutionUnit: null,
      resolutionIncrement: null,
      mainUnit: null,
      shiftUnit: null,
      shiftIncrement: 1,
      defaultSpan: 1,
      weekStartDay: null,
      // Used to force resolution to match whole ticks, to snap accordingly when using fillTicks in the UI
      forceFullTicks: null
    };
  }
  //endregion
  //region Init
  // private
  construct(config) {
    const me = this;
    super.construct(config);
    me.originalContinuous = me.continuous;
    me.ion({
      change: ({ action }) => {
        if (action !== "filter") {
          me.trigger("reconfigure", { supressRefresh: false });
        }
      },
      refresh: () => me.trigger("reconfigure", { supressRefresh: false }),
      endreconfigure: (event) => me.trigger("reconfigure", event)
    });
    if (me.startDate) {
      me.internalOnReconfigure();
      me.trigger("reconfigure");
    } else if (me.viewPreset) {
      const range = me.getAdjustedDates(/* @__PURE__ */ new Date());
      me.startDate = range.startDate;
      me.endDate = range.endDate;
    }
  }
  get isTimeAxis() {
    return true;
  }
  //endregion
  //region Configuration (reconfigure & consumePreset)
  /**
   * Reconfigures the time axis based on the config object supplied and generates the new 'ticks'.
   * @param {Object} config
   * @param {Boolean} [suppressRefresh]
   * @private
   */
  reconfigure(config, suppressRefresh = false, preventThrow = false) {
    const me = this, { owner } = me, oldConfig = {};
    let normalized = me.getAdjustedDates(config.startDate, config.endDate), isChanged = false;
    if (owner == null ? void 0 : owner.isBeforeMinDate(normalized.startDate)) {
      config.startDate = owner == null ? void 0 : owner.minDate;
      isChanged = true;
    }
    if (owner == null ? void 0 : owner.isAfterMaxDate(normalized.endDate)) {
      config.endDate = owner == null ? void 0 : owner.maxDate;
      isChanged = true;
    }
    if (isChanged) {
      normalized = me.getAdjustedDates(config.startDate, config.endDate);
    }
    if (me.trigger("beforeReconfigure", { startDate: normalized.startDate, endDate: normalized.endDate, config }) !== false) {
      me.trigger("beginReconfigure");
      me._configuredStartDate = config.startDate;
      me._configuredEndDate = config.endDate;
      for (const propName in config) {
        oldConfig[propName] = me[propName];
      }
      const viewPresetChanged = config.viewPreset && config.viewPreset !== me.viewPreset;
      if (viewPresetChanged) {
        preventThrow = me.isFiltered;
        me.filters.forEach((f) => f.disabled = false);
      }
      Object.assign(me, config);
      if (me.internalOnReconfigure(preventThrow, viewPresetChanged) === false) {
        return false;
      }
      me.trigger("endReconfigure", { suppressRefresh, config, oldConfig });
    }
  }
  internalOnReconfigure(preventThrow = false, viewPresetChanged) {
    const me = this;
    me.isConfigured = true;
    const adjusted = me.getAdjustedDates(me.startDate, me.endDate, true), normalized = me.getAdjustedDates(me.startDate, me.endDate), start = normalized.startDate, end = normalized.endDate;
    if (start >= end) {
      throw new Error(`Invalid start/end dates. Start date must be less than end date. Start date: ${start}. End date: ${end}.`);
    }
    const {
      unit,
      increment = 1
    } = me, ticks = me.generateTicks(start, end, unit, increment) || // Offer a fallback in case user did not generate any ticks at all.
    me.constructor.prototype.generateTicks.call(me, start, end, unit, increment);
    me.suspendEvents();
    me.maintainFilter = preventThrow;
    me.data = ticks;
    me.maintainFilter = false;
    const { count } = me;
    if (count === 0) {
      if (preventThrow) {
        if (viewPresetChanged) {
          me.disableFilters();
        }
        me.resumeEvents();
        return false;
      }
      throw new Error("Invalid time axis configuration or filter, please check your input data.");
    }
    me.startDate = me.first.startDate;
    me.endDate = me.last.endDate;
    me.unfilteredStartDate = start;
    me.unfilteredEndDate = end;
    me.resumeEvents();
    if (me.isContinuous) {
      me.adjustedStart = adjusted.startDate;
      me.adjustedEnd = DateHelper.getNext(count > 1 ? ticks[count - 1].startDate : adjusted.startDate, unit, increment, me.weekStartDay);
    } else {
      me.adjustedStart = me.startDate;
      me.adjustedEnd = me.endDate;
    }
    me.updateVisibleTickBoundaries();
    me.updateTickCache(true);
  }
  updateVisibleTickBoundaries() {
    const me = this, {
      count,
      unit,
      startDate,
      endDate,
      weekStartDay,
      increment = 1
    } = me;
    const startDenominator = DateHelper.getNormalizedUnitDuration(startDate, unit) * increment, endDenominator = DateHelper.getNormalizedUnitDuration(endDate, unit) * increment;
    do {
      me.visibleTickStart = (startDate - me.adjustedStart) / startDenominator;
      if (me.autoAdjust)
        me.visibleTickStart = Math.floor(me.visibleTickStart);
      if (me.visibleTickStart >= 1)
        me.adjustedStart = DateHelper.getNext(me.adjustedStart, unit, increment, weekStartDay);
    } while (me.visibleTickStart >= 1);
    do {
      me.visibleTickEnd = count - (me.adjustedEnd - endDate) / endDenominator;
      if (count - me.visibleTickEnd >= 1)
        me.adjustedEnd = DateHelper.getNext(me.adjustedEnd, unit, -1, weekStartDay);
    } while (count - me.visibleTickEnd >= 1);
    me.fullTicks = !me.visibleTickStart && me.visibleTickEnd === count;
  }
  /**
   * Get the currently used time unit for the ticks
   * @readonly
   * @member {DurationUnit} unit
   */
  /**
   * Get/set currently used preset
   * @property {Scheduler.preset.ViewPreset}
   */
  get viewPreset() {
    return this._viewPreset;
  }
  set viewPreset(preset) {
    const me = this;
    preset = pm.getPreset(preset);
    if (!preset.isViewPreset) {
      throw new Error("TimeAxis must be configured with the ViewPreset instance that the Scheduler is using");
    }
    me._viewPreset = preset;
    Object.assign(me, {
      unit: preset.bottomHeader.unit,
      increment: preset.bottomHeader.increment || 1,
      resolutionUnit: preset.timeResolution.unit,
      resolutionIncrement: preset.timeResolution.increment,
      mainUnit: preset.mainHeader.unit,
      shiftUnit: preset.shiftUnit || preset.mainHeader.unit,
      shiftIncrement: preset.shiftIncrement || 1,
      defaultSpan: preset.defaultSpan || 1,
      presetName: preset.id,
      // Weekview columns are updated upon 'datachanged' event on this object.
      // We have to pass headers in order to render them correctly (timeAxisViewModel is incorrect in required time)
      headers: preset.headers
    });
  }
  //endregion
  //region Getters & setters
  get weekStartDay() {
    var _a5;
    return (_a5 = this._weekStartDay) != null ? _a5 : DateHelper.weekStartDay;
  }
  // private
  get resolution() {
    return {
      unit: this.resolutionUnit,
      increment: this.resolutionIncrement
    };
  }
  // private
  set resolution(resolution) {
    this.resolutionUnit = resolution.unit;
    this.resolutionIncrement = resolution.increment;
  }
  get resolutionUnit() {
    return this.forceFullTicks ? this.unit : this._resolutionUnit;
  }
  get resolutionIncrement() {
    return this.forceFullTicks ? this.increment : this._resolutionIncrement;
  }
  //endregion
  //region Timespan & resolution
  /**
   * Changes the time axis timespan to the supplied start and end dates.
   *
   * **Note** This does **not** preserve the temporal scroll position. You may use
   * {@link Scheduler.view.Scheduler#function-setTimeSpan} to set the time axis and
   * maintain temporal scroll position (if possible).
   * @param {Date} newStartDate The new start date
   * @param {Date} [newEndDate] The new end date
   */
  setTimeSpan(newStartDate, newEndDate, preventThrow = false) {
    if (newEndDate && newStartDate - newEndDate === 0) {
      newEndDate = null;
    }
    const me = this, { startDate, endDate } = me.getAdjustedDates(newStartDate, newEndDate);
    if (me.startDate - startDate !== 0 || me.endDate - endDate !== 0) {
      return me.reconfigure({
        startDate,
        endDate
      }, false, preventThrow);
    }
  }
  /**
   * Moves the time axis by the passed amount and unit.
   *
   * NOTE: When using a filtered TimeAxis the result of `shift()` cannot be guaranteed, it might shift into a
   * filtered out span. It tries to be smart about it by shifting from unfiltered start and end dates.
   * If that solution does not work for your filtering setup, please call {@link #function-setTimeSpan} directly
   * instead.
   *
   * @param {Number} amount The number of units to jump
   * @param {String} [unit] The unit (Day, Week etc)
   */
  shift(amount, unit = this.shiftUnit) {
    const me = this;
    let { startDate, endDate } = me;
    if (me.isFiltered) {
      startDate = me.allRecords[0].startDate;
      endDate = me.allRecords[me.totalCount - 1].endDate;
    }
    let tries = 0;
    do {
      startDate = DateHelper.add(startDate, amount, unit);
      endDate = DateHelper.add(endDate, amount, unit);
    } while (tries++ < me.maxTraverseTries && me.setTimeSpan(startDate, endDate, {
      preventThrow: true
    }) === false);
  }
  /**
   * Moves the time axis forward in time in units specified by the view preset `shiftUnit`, and by the amount specified by the `shiftIncrement`
   * config of the current view preset.
   *
   * NOTE: When using a filtered TimeAxis the result of `shiftNext()` cannot be guaranteed, it might shift into a
   * filtered out span. It tries to be smart about it by shifting from unfiltered start and end dates.
   * If that solution does not work for your filtering setup, please call {@link #function-setTimeSpan} directly
   * instead.
   *
   * @param {Number} [amount] The number of units to jump forward
   */
  shiftNext(amount = this.shiftIncrement) {
    this.shift(amount);
  }
  /**
   * Moves the time axis backward in time in units specified by the view preset `shiftUnit`, and by the amount specified by the `shiftIncrement` config of the current view preset.
   *
   * NOTE: When using a filtered TimeAxis the result of `shiftPrev()` cannot be guaranteed, it might shift into a
   * filtered out span. It tries to be smart about it by shifting from unfiltered start and end dates.
   * If that solution does not work for your filtering setup, please call {@link #function-setTimeSpan} directly
   * instead.
   *
   * @param {Number} [amount] The number of units to jump backward
   */
  shiftPrevious(amount = this.shiftIncrement) {
    this.shift(-amount);
  }
  //endregion
  //region Filter & continuous
  /**
   * Filter the time axis by a function (and clears any existing filters first). The passed function will be called with each tick in time axis.
   * If the function returns `true`, the 'tick' is included otherwise it is filtered. If all ticks are filtered out
   * the time axis is considered invalid, triggering `invalidFilter` and then removing the filter.
   * @param {Function} fn The function to be called, it will receive an object with `startDate`/`endDate` properties, and `index` of the tick.
   * @param {Object} [thisObj] `this` reference for the function
   * @typings {Promise<any|null>}
   */
  filterBy(fn2, thisObj = this) {
    const me = this;
    me.filters.clear();
    super.filterBy((tick, index) => fn2.call(thisObj, tick.data, index));
  }
  filter() {
    var _a5;
    const me = this;
    me.viewportCenterDate = (_a5 = me.owner) == null ? void 0 : _a5.viewportCenterDate;
    const retVal = super.filter(...arguments);
    if (!me.maintainFilter && me.count === 0) {
      me.resumeEvents();
      me.trigger("invalidFilter");
      me.disableFilters();
    }
    return retVal;
  }
  disableFilters() {
    this.filters.forEach((f) => f.disabled = true);
    this.filter();
  }
  triggerFilterEvent(event) {
    const me = this, { owner } = me;
    if (!event.filters.count) {
      me.continuous = me.originalContinuous;
    } else {
      me.continuous = false;
    }
    me.updateTickCache();
    if ((owner == null ? void 0 : owner.infiniteScroll) && !me.continuous) {
      owner.timeAxisViewModel.ion({
        update: () => {
          owner.shiftToDate(me.viewportCenterDate, {
            visibleDate: me.viewportCenterDate,
            block: "center"
          });
        },
        once: true
      });
    }
    super.triggerFilterEvent(event);
  }
  /**
   * Returns `true` if the time axis is continuous (will return `false` when filtered)
   * @property {Boolean}
   */
  get isContinuous() {
    return this.continuous !== false && !this.isFiltered;
  }
  //endregion
  //region Dates
  getAdjustedDates(startDate, endDate, forceAdjust = false) {
    var _a5, _b2;
    const me = this, { owner } = me;
    if (endDate && startDate - endDate === 0) {
      endDate = null;
    }
    startDate = startDate || me.startDate;
    endDate = endDate || DateHelper.add(startDate, me.defaultSpan, me.mainUnit);
    const startDateAdjusted = me.floorDate(startDate, false, me.autoAdjust ? me.mainUnit : me.unit, 1), endDateAdjusted = me.ceilDate(endDate, false, me.autoAdjust ? me.mainUnit : me.unit, 1), diffStartEndDates = endDateAdjusted - startDateAdjusted, startDateAdjMinDate = (_a5 = owner == null ? void 0 : owner.getMinDateOrDate(startDateAdjusted)) != null ? _a5 : startDateAdjusted;
    let endDateAdjMaxDate = (_b2 = owner == null ? void 0 : owner.getMaxDateOrDate(endDateAdjusted)) != null ? _b2 : endDateAdjusted;
    if (endDateAdjMaxDate <= startDateAdjMinDate) {
      endDateAdjMaxDate = owner == null ? void 0 : owner.getMaxDateOrDate(DateHelper.add(startDateAdjMinDate, diffStartEndDates));
    }
    return me.autoAdjust || forceAdjust ? {
      startDate: startDateAdjMinDate,
      endDate: endDateAdjMaxDate
    } : {
      startDate,
      endDate
    };
  }
  /**
   * The current start date of the time axis.
   * {@note}This is after filtering for {@link Scheduler.view.Scheduler#config-workingTime} and so may not be the same as
   * the {@link #property-unfilteredStartDate configured start date}.{/note}
   * @property {Date}
   */
  get startDate() {
    return this._start || (this.first ? new Date(this.first.startDate) : null);
  }
  set startDate(start) {
    this._start = DateHelper.parse(start);
  }
  /**
   * The current end date of the time axis.
   * {@note}This is after filtering for {@link Scheduler.view.Scheduler#config-workingTime} and so may not be the same as
   * the {@link #property-unfilteredEndDate configured start date}.{/note}
   * @property {Date}
   */
  get endDate() {
    return this._end || (this.last ? new Date(this.last.endDate) : null);
  }
  set endDate(end) {
    if (end)
      this._end = DateHelper.parse(end);
  }
  // used in performance critical code for comparisons
  get startMS() {
    return this._startMS;
  }
  // used in performance critical code for comparisons
  get endMS() {
    return this._endMS;
  }
  // Floors a date and optionally snaps it to one of the following resolutions:
  // 1. 'resolutionUnit'. If param 'resolutionUnit' is passed, the date will simply be floored to this unit.
  // 2. If resolutionUnit is not passed: If date should be snapped relative to the timeaxis start date,
  // the resolutionUnit of the timeAxis will be used, or the timeAxis 'mainUnit' will be used to snap the date
  //
  // returns a copy of the original date
  // private
  floorDate(date2, relativeToStart, resolutionUnit, incr) {
    relativeToStart = relativeToStart !== false;
    const me = this, relativeTo = relativeToStart ? DateHelper.clone(me.startDate) : null, increment = incr || me.resolutionIncrement, unit = resolutionUnit || (relativeToStart ? me.resolutionUnit : me.mainUnit), snap = (value2, increment2) => Math.floor(value2 / increment2) * increment2;
    if (relativeToStart) {
      return DateHelper.floor(date2, { unit, magnitude: increment }, relativeTo);
    }
    const dt = DateHelper.clone(date2);
    if (unit === "week") {
      const day2 = dt.getDay() || 7, startDay = me.weekStartDay || 7;
      DateHelper.add(DateHelper.startOf(dt, "day", false), day2 >= startDay ? startDay - day2 : -(7 - startDay + day2), "day", false);
      if (dt.getDay() !== startDay && dt.getHours() === 23) {
        DateHelper.add(dt, 1, "hour", false);
      }
    } else {
      DateHelper.startOf(dt, unit, false);
      const modifier = ["day", "year"].includes(unit) ? 1 : 0, useUnit = unit === "day" ? "date" : unit, snappedValue = snap(DateHelper.get(dt, useUnit) - modifier, increment) + modifier;
      DateHelper.set(dt, useUnit, snappedValue);
    }
    return dt;
  }
  /**
   * Rounds the date to nearest unit increment
   * @private
   */
  roundDate(date2, relativeTo, resolutionUnit = this.resolutionUnit, increment = this.resolutionIncrement || 1) {
    const me = this, dt = DateHelper.clone(date2);
    relativeTo = DateHelper.clone(relativeTo || me.startDate);
    switch (resolutionUnit) {
      case "week": {
        DateHelper.startOf(dt, "day", false);
        let distanceToWeekStartDay = dt.getDay() - me.weekStartDay, toAdd;
        if (distanceToWeekStartDay < 0) {
          distanceToWeekStartDay = 7 + distanceToWeekStartDay;
        }
        if (Math.round(distanceToWeekStartDay / 7) === 1) {
          toAdd = 7 - distanceToWeekStartDay;
        } else {
          toAdd = -distanceToWeekStartDay;
        }
        return DateHelper.add(dt, toAdd, "day", false);
      }
      case "month": {
        const nbrMonths = DateHelper.diff(relativeTo, dt, "month") + DateHelper.as("month", dt.getDay() / DateHelper.daysInMonth(dt)), snappedMonths = Math.round(nbrMonths / increment) * increment;
        return DateHelper.add(relativeTo, snappedMonths, "month", false);
      }
      case "quarter":
        DateHelper.startOf(dt, "month", false);
        return DateHelper.add(dt, 3 - dt.getMonth() % 3, "month", false);
      default: {
        const duration = DateHelper.as(resolutionUnit, DateHelper.diff(relativeTo, dt)), offset = resolutionUnit === "year" ? 0 : DateHelper.as(resolutionUnit, relativeTo.getTimezoneOffset() - dt.getTimezoneOffset(), "minute"), snappedDuration = Math.round((duration + offset) / increment) * increment;
        return DateHelper.add(relativeTo, snappedDuration - offset, resolutionUnit, false);
      }
    }
  }
  // private
  ceilDate(date2, relativeToStart, resolutionUnit, increment) {
    const me = this;
    relativeToStart = relativeToStart !== false;
    increment = increment || (relativeToStart ? me.resolutionIncrement : 1);
    const unit = resolutionUnit || (relativeToStart ? me.resolutionUnit : me.mainUnit), dt = DateHelper.clone(date2);
    let doCall = false;
    switch (unit) {
      case "minute":
        doCall = !DateHelper.isStartOf(dt, "minute");
        break;
      case "hour":
        doCall = !DateHelper.isStartOf(dt, "hour");
        break;
      case "day":
      case "date":
        doCall = !DateHelper.isStartOf(dt, "day");
        break;
      case "week":
        DateHelper.startOf(dt, "day", false);
        doCall = dt.getDay() !== me.weekStartDay || !DateHelper.isEqual(dt, date2);
        break;
      case "month":
        DateHelper.startOf(dt, "day", false);
        doCall = dt.getDate() !== 1 || !DateHelper.isEqual(dt, date2);
        break;
      case "quarter":
        DateHelper.startOf(dt, "day", false);
        doCall = dt.getMonth() % 3 !== 0 || dt.getDate() !== 1 || !DateHelper.isEqual(dt, date2);
        break;
      case "year":
        DateHelper.startOf(dt, "day", false);
        doCall = dt.getMonth() !== 0 || dt.getDate() !== 1 || !DateHelper.isEqual(dt, date2);
        break;
    }
    if (doCall) {
      return DateHelper.getNext(dt, unit, increment, me.weekStartDay);
    }
    return dt;
  }
  //endregion
  //region Ticks
  get include() {
    return this._include;
  }
  set include(include) {
    const me = this;
    me._include = include;
    me.continuous = !include;
    if (!me.isConfiguring) {
      me.startDate = me._configuredStartDate;
      me.endDate = me._configuredEndDate;
      me.internalOnReconfigure();
      me.trigger("includeChange");
    }
  }
  // Check if a certain date is included based on timeAxis.include rules
  processExclusion(startDate, endDate, unit) {
    const { include } = this;
    if (include) {
      return Object.entries(include).some(([includeUnit, rule]) => {
        if (!rule) {
          return false;
        }
        const { from, to } = rule;
        if (DateHelper.compareUnits("day", unit) >= 0 && DateHelper.getLargerUnit(includeUnit) === unit) {
          if (from) {
            DateHelper.set(startDate, includeUnit, from);
          }
          if (to) {
            let stepUnit = unit;
            if (unit === "day") {
              stepUnit = "date";
            }
            DateHelper.set(endDate, {
              [stepUnit]: DateHelper.get(endDate, stepUnit) - 1,
              [includeUnit]: to
            });
          }
        }
        if (DateHelper.compareUnits(includeUnit, unit) >= 0) {
          const datePart = includeUnit === "day" ? startDate.getDay() : DateHelper.get(startDate, includeUnit);
          if (from && datePart < from || to && datePart >= to) {
            return true;
          }
        }
      });
    }
    return false;
  }
  // Calculate constants used for exclusion when scaling within larger ticks
  initExclusion() {
    Object.entries(this.include).forEach(([unit, rule]) => {
      if (rule) {
        const { from, to } = rule;
        rule.lengthFactor = DateHelper.getUnitToBaseUnitRatio(unit, DateHelper.getLargerUnit(unit)) / (to - from);
        rule.lengthFactorExcl = DateHelper.getUnitToBaseUnitRatio(unit, DateHelper.getLargerUnit(unit)) / (to - from - 1);
        rule.center = from + from / (rule.lengthFactor - 1);
      }
    });
  }
  /**
   * Method generating the ticks for this time axis. Should return an array of ticks . Each tick is an object of the following structure:
   * ```
   * {
   *    startDate : ..., // start date
   *    endDate   : ...  // end date
   * }
   * ```
   * Take notice, that this function either has to be called with `start`/`end` parameters, or create those variables.
   *
   * To see it in action please check out our [TimeAxis](https://bryntum.com/products/scheduler/examples/timeaxis/) example and navigate to "Compressed non-working time" tab.
   *
   * @member {Function} generateTicks
   * @param {Date} axisStartDate The start date of the interval
   * @param {Date} axisEndDate The end date of the interval
   * @param {DurationUnit} unit The unit of the time axis
   * @param {Number} increment The increment for the unit specified.
   * @returns {Array|undefined} ticks The ticks representing the time axis, or no return value to use the default tick generation
   */
  updateGenerateTicks() {
    if (!this.isConfiguring) {
      this.reconfigure(this);
    }
  }
  _generateTicks(axisStartDate, axisEndDate, unit = this.unit, increment = this.increment) {
    const me = this, ticks = [], usesExclusion = Boolean(me.include);
    me.validateTickCount();
    let intervalEnd, tickEnd, isExcluded, dstDiff = 0, { startDate, endDate } = me.getAdjustedDates(axisStartDate, axisEndDate), tryRelyingOnExclusion = false;
    me.tickCache = {};
    if (usesExclusion) {
      me.initExclusion();
    }
    while (startDate < endDate) {
      intervalEnd = DateHelper.getNext(startDate, unit, increment, me.weekStartDay);
      if (!me.autoAdjust && intervalEnd > endDate && !tryRelyingOnExclusion) {
        intervalEnd = new Date(endDate.getTime());
      }
      if (unit === "hour" && increment > 1 && ticks.length > 0 && dstDiff === 0) {
        const prev = ticks[ticks.length - 1];
        dstDiff = (prev.startDate.getHours() + increment) % 24 - prev.endDate.getHours();
        if (dstDiff !== 0) {
          intervalEnd = DateHelper.add(intervalEnd, dstDiff, "hour");
        }
      }
      isExcluded = false;
      if (usesExclusion) {
        tickEnd = new Date(intervalEnd.getTime());
        isExcluded = me.processExclusion(startDate, intervalEnd, unit);
        if (tryRelyingOnExclusion) {
          if (!me.autoAdjust && intervalEnd > endDate) {
            startDate = tickEnd;
            continue;
          }
        } else if (intervalEnd < startDate) {
          tryRelyingOnExclusion = true;
          continue;
        }
      } else {
        tickEnd = intervalEnd;
      }
      if (!isExcluded) {
        ticks.push({
          id: ticks.length + 1,
          startDate,
          endDate: intervalEnd
        });
        me.tickCache[startDate.getTime()] = ticks.length - 1;
      }
      startDate = tickEnd;
    }
    return ticks;
  }
  /**
   * How many ticks are visible across the TimeAxis.
   *
   * Usually, this is an integer because {@link #config-autoAdjust} means that the start and end
   * dates are adjusted to be on tick boundaries.
   * @property {Number}
   * @internal
   */
  get visibleTickTimeSpan() {
    const me = this;
    return me.isContinuous ? me.visibleTickEnd - me.visibleTickStart : me.count;
  }
  /**
   * Gets a tick "coordinate" representing the date position on the timescale. Returns -1 if the date is not part of the time axis.
   * @param {Date} date the date
   * @returns {Number} the tick position on the scale or -1 if the date is not part of the time axis
   */
  getTickFromDate(date2) {
    var _a5, _b2;
    const me = this, ticks = me.records, dateMS = (_b2 = (_a5 = date2.getTime) == null ? void 0 : _a5.call(date2)) != null ? _b2 : date2;
    let begin = 0, end = ticks.length - 1, middle, tick, tickStart, tickEnd;
    if (!ticks.length || dateMS < ticks[0].startDateMS || dateMS > ticks[end].endDateMS) {
      return -1;
    }
    if (me.isContinuous) {
      while (begin < end) {
        middle = begin + end + 1 >> 1;
        if (dateMS > ticks[middle].endDateMS) {
          begin = middle + 1;
        } else if (dateMS < ticks[middle].startDateMS) {
          end = middle - 1;
        } else {
          begin = middle;
        }
      }
      tick = ticks[begin];
      tickStart = tick.startDateMS;
      if (dateMS > tickStart) {
        tickEnd = tick.endDateMS;
        begin += (dateMS - tickStart) / (tickEnd - tickStart);
      }
      return Math.min(Math.max(begin, me.visibleTickStart), me.visibleTickEnd);
    } else {
      for (let i = 0; i <= end; i++) {
        tickEnd = ticks[i].endDateMS;
        if (dateMS <= tickEnd) {
          tickStart = ticks[i].startDateMS;
          tick = i + (dateMS > tickStart ? (dateMS - tickStart) / (tickEnd - tickStart) : 0);
          return tick;
        }
      }
    }
  }
  getSnappedTickFromDate(date2) {
    const startTickIdx = Math.floor(this.getTickFromDate(date2));
    return this.getAt(startTickIdx);
  }
  /**
   * Gets the time represented by a tick "coordinate".
   * @param {Number} tick the tick "coordinate"
   * @param {'floor'|'round'|'ceil'} [roundingMethod] Rounding method to use. 'floor' to take the tick (lowest header
   * in a time axis) start date, 'round' to round the value to nearest increment or 'ceil' to take the tick end date
   * @returns {Date} The date to represented by the tick "coordinate", or null if invalid.
   */
  getDateFromTick(tick, roundingMethod) {
    const me = this;
    if (tick === me.visibleTickEnd) {
      return me.endDate;
    }
    const wholeTick = Math.floor(tick), fraction = tick - wholeTick, t = me.getAt(wholeTick);
    if (!t) {
      return null;
    }
    const start = wholeTick === 0 && me.isContinuous ? me.adjustedStart : t.startDate, end = wholeTick === me.count - 1 && me.isContinuous ? me.adjustedEnd : t.endDate;
    let date2 = DateHelper.add(start, fraction * (end - start), "millisecond");
    if (roundingMethod) {
      date2 = me[roundingMethod + "Date"](date2);
    }
    return date2;
  }
  /**
   * Calculated max allowed `endDate` according to `maxAllowedTickCount` value and current viewPreset
   * @private
   */
  get maxAllowedEndDate() {
    const incrementDiff = this.maxAllowedTickCount * DateHelper.asMilliseconds(this.increment, this.unit), maxAllowedEndDate = DateHelper.add(DateHelper.parse(this.startDate), incrementDiff);
    return maxAllowedEndDate;
  }
  /**
   * Checks if date range is in bounds of allowed and throws and exception if not
   * @private
   */
  validateTickCount() {
    if (this.endDate > this.maxAllowedEndDate) {
      console.error("Configured date range will result in a too long time axis. If you require a long timeline, please use InfiniteScroll feature.");
    }
  }
  /**
   * Returns the ticks of the timeaxis in an array of objects with a "startDate" and "endDate".
   * @property {Scheduler.model.TimeSpan[]}
   */
  get ticks() {
    return this.records;
  }
  /**
   * Caches ticks and start/end dates for faster processing during rendering of events.
   * @private
   */
  updateTickCache(onlyStartEnd = false) {
    const me = this;
    if (me.count) {
      me._start = me.first.startDate;
      me._end = me.last.endDate;
      me._startMS = me.startDate.getTime();
      me._endMS = me.endDate.getTime();
    } else {
      me._start = me._end = me._startMs = me._endMS = null;
    }
    if (!onlyStartEnd) {
      me.tickCache = {};
      me.forEach((tick, i) => me.tickCache[tick.startDate.getTime()] = i);
    }
  }
  //endregion
  //region Axis
  /**
   * Returns true if the passed date is inside the span of the current time axis.
   * @param {Date} date The date to query for
   * @returns {Boolean} true if the date is part of the time axis
   */
  dateInAxis(date2, inclusiveEnd = false) {
    const me = this, axisStart = me.startDate, axisEnd = me.endDate;
    if (me.isContinuous) {
      return inclusiveEnd ? DateHelper.betweenLesserEqual(date2, axisStart, axisEnd) : DateHelper.betweenLesser(date2, axisStart, axisEnd);
    } else {
      const length = me.count;
      let tickStart, tickEnd, tick;
      for (let i = 0; i < length; i++) {
        tick = me.getAt(i);
        tickStart = tick.startDate;
        tickEnd = tick.endDate;
        if (inclusiveEnd && date2 <= tickEnd || !inclusiveEnd && date2 < tickEnd) {
          return date2 >= tickStart;
        }
      }
    }
    return false;
  }
  /**
   * Returns true if the passed timespan is part of the current time axis (in whole or partially).
   * @param {Date} start The start date
   * @param {Date} end The end date
   * @returns {Boolean} true if the timespan is part of the timeaxis
   */
  timeSpanInAxis(start, end) {
    const me = this;
    if (!end || end.getTime() === start.getTime()) {
      return this.dateInAxis(start, true);
    }
    if (me.isContinuous) {
      return DateHelper.intersectSpans(start, end, me.startDate, me.endDate);
    }
    return start < me.startDate && end > me.endDate || me.getTickFromDate(start) !== me.getTickFromDate(end);
  }
  // Accepts a TimeSpan model (uses its cached MS values to be a bit faster during rendering)
  isTimeSpanInAxis(timeSpan) {
    var _a5;
    const me = this, { startMS, endMS } = me, { startDateMS } = timeSpan, endDateMS = (_a5 = timeSpan.endDateMS) != null ? _a5 : timeSpan.meta.endDateCached;
    if (!startDateMS || !endDateMS) {
      return false;
    }
    if (endDateMS === startDateMS) {
      return me.dateInAxis(timeSpan.startDate, true);
    }
    if (me.isContinuous) {
      return endDateMS > startMS && startDateMS < endMS;
    }
    const startTick = me.getTickFromDate(startDateMS), endTick = me.getTickFromDate(endDateMS);
    if (startTick === me.count && DateHelper.isEqual(timeSpan.startDate, me.last.endDate) || endTick === 0 && DateHelper.isEqual(timeSpan.endDate, me.first.startDate)) {
      return false;
    }
    return (
      // Spanning entire axis
      startDateMS < startMS && endDateMS > endMS || // Unintentionally 0 wide (ticks excluded or outside)
      startTick !== endTick
    );
  }
  //endregion
  //region Iteration
  /**
   * Calls the supplied iterator function once per interval. The function will be called with four parameters, startDate endDate, index, isLastIteration.
   * @internal
   * @param {DurationUnit} unit The unit to use when iterating over the timespan
   * @param {Number} increment The increment to use when iterating over the timespan
   * @param {Function} iteratorFn The function to call
   * @param {Object} [thisObj] `this` reference for the function
   */
  forEachAuxInterval(unit, increment = 1, iteratorFn, thisObj = this) {
    const end = this.endDate;
    let dt = this.startDate, i = 0, intervalEnd;
    if (dt > end) {
      throw new Error("Invalid time axis configuration");
    }
    while (dt < end) {
      intervalEnd = DateHelper.min(DateHelper.getNext(dt, unit, increment, this.weekStartDay), end);
      iteratorFn.call(thisObj, dt, intervalEnd, i, intervalEnd >= end);
      dt = intervalEnd;
      i++;
    }
  }
  //endregion
};
TimeAxis._$name = "TimeAxis";

// ../Scheduler/lib/Scheduler/view/model/TimeAxisViewModel.js
var TimeAxisViewModel = class extends Events_default() {
  //region Default config
  static get defaultConfig() {
    return {
      /**
       * The time axis providing the underlying data to be visualized
       * @config {Scheduler.data.TimeAxis}
       * @internal
       */
      timeAxis: null,
      /**
       * The available width/height, this is normally not known by the consuming UI component using this model
       * class until it has been fully rendered. The consumer of this model should set
       * {@link #property-availableSpace} when its width has changed.
       * @config {Number}
       * @internal
       */
      availableSpace: null,
      /**
       * The "tick width" for horizontal mode or "tick height" for vertical mode, to use for the cells in the
       * bottom most header row.
       * This value is normally read from the {@link Scheduler.preset.ViewPreset viewPreset}
       * @config {Number}
       * @default
       * @internal
       */
      tickSize: 100,
      /**
       * true if there is a requirement to be able to snap events to a certain view resolution.
       * This has implications of the {@link #config-tickSize} that can be used, since all widths must be in even pixels.
       * @config {Boolean}
       * @default
       * @internal
       */
      snap: false,
      /**
       * true if cells in the bottom-most row should be fitted to the {@link #property-availableSpace available space}.
       * @config {Boolean}
       * @default
       * @internal
       */
      forceFit: false,
      headers: null,
      mode: "horizontal",
      // or 'vertical'
      //used for Exporting. Make sure the tick columns are not recalculated when resizing.
      suppressFit: false,
      // cache of the config currently used.
      columnConfig: [],
      // the view preset name to apply initially
      viewPreset: null,
      // The default header level to draw column lines for
      columnLinesFor: null,
      originalTickSize: null,
      headersDatesCache: []
    };
  }
  //endregion
  //region Init & destroy
  construct(config) {
    const me = this;
    me.unitToPixelsCache = {};
    super.construct(config);
    const viewPreset = me.timeAxis.viewPreset || me.viewPreset;
    if (viewPreset) {
      if (viewPreset instanceof ViewPreset) {
        me.consumeViewPreset(viewPreset);
      } else {
        const preset = pm.getPreset(viewPreset);
        preset && me.consumeViewPreset(preset);
      }
    }
    me.timeAxis.ion({ reconfigure: "onTimeAxisReconfigure", thisObj: me });
    me.configured = true;
  }
  doDestroy() {
    var _a5, _b2;
    (_b2 = (_a5 = this.timeAxis).un) == null ? void 0 : _b2.call(_a5, "reconfigure", this.onTimeAxisReconfigure, this);
    super.doDestroy();
  }
  /**
   * Returns an array representing the headers of the current timeAxis. Each element is an array representing the cells for that level in the header.
   * @returns {Object[]} An array of headers, each element being an array representing each cell (with start date and end date) in the timeline representation.
   * @internal
   */
  get columnConfig() {
    return this._columnConfig;
  }
  set columnConfig(config) {
    this._columnConfig = config;
  }
  get headers() {
    return this._headers;
  }
  set headers(headers) {
    if (headers && headers.length && headers[headers.length - 1].cellGenerator) {
      throw new Error("`cellGenerator` cannot be used for the bottom level of your headers. Use TimeAxis#generateTicks() instead.");
    }
    this._headers = headers;
  }
  get isTimeAxisViewModel() {
    return true;
  }
  //endregion
  //region Events
  /**
   * Fires after the model has been updated.
   * @event update
   * @param {Scheduler.view.model.TimeAxisViewModel} source The model instance
   */
  /**
   * Fires after the model has been reconfigured.
   * @event reconfigure
   * @param {Scheduler.view.model.TimeAxisViewModel} source The model instance
   */
  //endregion
  //region Mode
  /**
   * Using horizontal mode?
   * @returns {Boolean}
   * @readonly
   * @internal
   */
  get isHorizontal() {
    return this.mode !== "vertical";
  }
  /**
   * Using vertical mode?
   * @returns {Boolean}
   * @readonly
   * @internal
   */
  get isVertical() {
    return this.mode === "vertical";
  }
  /**
   * Gets/sets the forceFit value for the model. Setting it will cause it to update its contents and fire the
   * {@link #event-update} event.
   * @property {Boolean}
   * @internal
   */
  set forceFit(value2) {
    if (value2 !== this._forceFit) {
      this._forceFit = value2;
      this.update();
    }
  }
  //endregion
  //region Reconfigure & update
  reconfigure(config) {
    this.headers = null;
    this.setConfig(config);
    this.trigger("reconfigure");
  }
  onTimeAxisReconfigure({ source: timeAxis, suppressRefresh }) {
    if (this.viewPreset !== timeAxis.viewPreset) {
      this.consumeViewPreset(timeAxis.viewPreset);
    }
    if (!suppressRefresh && timeAxis.count > 0) {
      this.update();
    }
  }
  /**
   * Updates the view model current timeAxis configuration and available space.
   * @param {Number} [availableSpace] The available space for the rendering of the axis (used in forceFit mode)
   * @param {Boolean} [silent] Pass `true` to suppress the firing of the `update` event.
   * @param {Boolean} [forceUpdate] Pass `true` to fire the `update` event even if the size has not changed.
   * @internal
   */
  update(availableSpace, silent = false, forceUpdate = false) {
    var _a5;
    const me = this, { timeAxis, headers } = me, spaceAvailable = availableSpace !== 0;
    if (me.isConfiguring || spaceAvailable && me._availableSpace === availableSpace) {
      if (forceUpdate) {
        me.trigger("update");
      }
      return;
    }
    me._availableSpace = Math.max(availableSpace || me.availableSpace || 0, 0);
    if (typeof me.availableSpace !== "number") {
      throw new Error("Invalid available space provided to TimeAxisModel");
    }
    me.columnConfig = [];
    const tickSize = me._tickSize = me.calculateTickSize(me.originalTickSize);
    if (typeof tickSize !== "number" || tickSize < 0) {
      throw new Error("Invalid timeAxis tick size");
    }
    me.unitToPixelsCache = {};
    me._totalSize = null;
    const oldWeekStartDay = DateHelper.weekStartDay;
    if (typeof ((_a5 = me.owner) == null ? void 0 : _a5.weekStartDay) === "number") {
      DateHelper._weekStartDay = timeAxis.weekStartDay;
    }
    for (let pos = 0, { length } = headers; pos < length; pos++) {
      const header = headers[pos];
      if (header.cellGenerator) {
        const headerCells = header.cellGenerator.call(me, timeAxis.startDate, timeAxis.endDate);
        me.columnConfig[pos] = me.createHeaderRow(pos, header, headerCells);
      } else {
        me.columnConfig[pos] = me.createHeaderRow(pos, header);
      }
    }
    if (me.owner) {
      DateHelper._weekStartDay = oldWeekStartDay;
    }
    if (!silent) {
      me.trigger("update");
    }
  }
  //endregion
  //region Date / position mapping
  /**
   * Returns the distance in pixels for a timespan with the given start and end date.
   * @param {Date} start start date
   * @param {Date} end end date
   * @returns {Number} The length of the time span
   * @category Date mapping
   */
  getDistanceBetweenDates(start, end) {
    return this.getPositionFromDate(end) - this.getPositionFromDate(start);
  }
  /**
   * Returns the distance in pixels for a time span
   * @param {Number} durationMS Time span duration in ms
   * @returns {Number} The length of the time span
   * @category Date mapping
   */
  getDistanceForDuration(durationMs) {
    return this.getSingleUnitInPixels("millisecond") * durationMs;
  }
  /**
   * Gets the position of a date on the projected time axis or -1 if the date is not in the timeAxis.
   * @param {Date} date the date to query for.
   * @returns {Number} the coordinate representing the date
   * @category Date mapping
   */
  getPositionFromDate(date2, options = {}) {
    const tick = this.getScaledTick(date2, options);
    if (tick === -1) {
      return -1;
    }
    return this.tickSize * (tick - this.timeAxis.visibleTickStart);
  }
  // Translates a tick along the time axis to facilitate scaling events when excluding certain days or hours
  getScaledTick(date2, { respectExclusion, snapToNextIncluded, isEnd, min: min4, max: max3 }) {
    const { timeAxis } = this, { include, unit } = timeAxis;
    let tick = timeAxis.getTickFromDate(date2);
    if (tick !== -1 && respectExclusion && include) {
      let tickChanged = false;
      if (include.hour && DateHelper.compareUnits(unit, "hour") > 0 && unit !== "day") {
        const { from, to, lengthFactor, center } = include.hour, originalHours = date2.getHours(), croppedHours = Math.min(Math.max(originalHours, from), to);
        if (!snapToNextIncluded && croppedHours !== originalHours) {
          return -1;
        }
        const fractionalHours = croppedHours + date2.getMinutes() / 60, hoursFromCenter = center - fractionalHours, newHours = center - hoursFromCenter * lengthFactor;
        date2 = DateHelper.add(date2, newHours - originalHours, "h");
        tickChanged = true;
      }
      if (include.day && DateHelper.compareUnits(unit, "day") > 0) {
        const { from, to, lengthFactor, center } = include.day;
        let checkDay = date2.getDay();
        if (isEnd && date2.getHours() === 0 && date2.getMinutes() === 0 && date2.getSeconds() === 0 && date2.getMilliseconds() === 0) {
          if (--checkDay < 0) {
            checkDay = 6;
          }
        }
        let addDays = 0;
        if (checkDay < from || checkDay >= to) {
          if (snapToNextIncluded) {
            if (isEnd) {
              addDays = (to - checkDay - 8) % 7;
            } else {
              addDays = (from - checkDay + 7) % 7;
            }
            date2 = DateHelper.add(date2, addDays, "d");
            date2 = DateHelper.startOf(date2, "d", false, timeAxis.weekStartDay);
            if (max3 && date2.getTime() >= max3 || min4 && date2.getTime() <= min4) {
              return -1;
            }
          } else {
            return -1;
          }
        }
        const { weekStartDay } = timeAxis, fixedCenter = date2.getDay() === 0 ? 0 : center, fractionalDay = date2.getDay() + date2.getHours() / 24, daysFromCenter = fixedCenter - fractionalDay, newDay = fixedCenter - daysFromCenter * lengthFactor;
        date2 = DateHelper.add(date2, newDay - fractionalDay + weekStartDay, "d");
        tickChanged = true;
      }
      if (tickChanged) {
        date2 = DateHelper.constrain(date2, timeAxis.startDate, timeAxis.endDate);
        tick = timeAxis.getTickFromDate(date2);
      }
    }
    return tick;
  }
  /**
   * Gets the date for a position on the time axis
   * @param {Number} position The page X or Y coordinate
   * @param {'floor'|'round'|'ceil'} [roundingMethod] Rounding method to use. 'floor' to take the tick (lowest header
   * in a time axis) start date, 'round' to round the value to nearest increment or 'ceil' to take the tick end date
   * @param {Boolean} [allowOutOfRange=false] By default, this returns `null` if the position is outside
   * of the time axis. Pass `true` to attempt to calculate a date outside of the time axis.
   * @returns {Date} the Date corresponding to the xy coordinate
   * @category Date mapping
   */
  getDateFromPosition(position, roundingMethod, allowOutOfRange = false) {
    const me = this, { timeAxis } = me, tick = me.getScaledPosition(position) / me.tickSize + timeAxis.visibleTickStart;
    if (tick < 0 || tick > timeAxis.count) {
      if (allowOutOfRange) {
        let result;
        if (tick < 0) {
          result = DateHelper.add(timeAxis.startDate, tick, timeAxis.unit);
        } else {
          result = DateHelper.add(timeAxis.endDate, tick - timeAxis.count, timeAxis.unit);
        }
        if (roundingMethod) {
          result = timeAxis[roundingMethod + "Date"](result);
        }
        return result;
      }
      return null;
    }
    return timeAxis.getDateFromTick(tick, roundingMethod);
  }
  // Translates a position along the time axis to facilitate scaling events when excluding certain days or hours
  getScaledPosition(position) {
    const { include, unit, weekStartDay } = this.timeAxis;
    if (include) {
      const dayWidth = this.getSingleUnitInPixels("day");
      if (include.day && DateHelper.compareUnits(unit, "day") > 0) {
        const { from, lengthFactor } = include.day, positionInWeek = position % (dayWidth * 7), weekStartPosition = position - positionInWeek;
        position = positionInWeek / lengthFactor + (from - weekStartDay) * dayWidth + weekStartPosition;
      }
      if (include.hour && DateHelper.compareUnits(unit, "hour") > 0 && unit !== "day") {
        const { from, lengthFactorExcl } = include.hour, hourWidth = this.getSingleUnitInPixels("hour"), positionInDay = position % dayWidth, dayStartPosition = position - positionInDay;
        position = positionInDay / lengthFactorExcl + from * hourWidth + dayStartPosition;
      }
    }
    return position;
  }
  /**
   * Returns the amount of pixels for a single unit
   * @internal
   * @returns {Number} The unit in pixel
   */
  getSingleUnitInPixels(unit) {
    const me = this;
    return me.unitToPixelsCache[unit] || (me.unitToPixelsCache[unit] = DateHelper.getUnitToBaseUnitRatio(me.timeAxis.unit, unit, true) * me.tickSize / me.timeAxis.increment);
  }
  /**
   * Returns the pixel increment for the current view resolution.
   * @internal
   * @returns {Number} The increment
   */
  get snapPixelAmount() {
    if (this.snap) {
      const { resolution } = this.timeAxis;
      return (resolution.increment || 1) * this.getSingleUnitInPixels(resolution.unit);
    }
    return 1;
  }
  //endregion
  //region Sizes
  /**
   * Get/set the current time column size (the width or height of a cell in the bottom-most time axis header row,
   * depending on mode)
   * @internal
   * @property {Number}
   */
  get tickSize() {
    return this._tickSize;
  }
  set tickSize(size) {
    this.setTickSize(size, false);
  }
  setTickSize(size, suppressEvent) {
    this._tickSize = this.originalTickSize = size;
    this.update(void 0, suppressEvent);
  }
  get timeResolution() {
    return this.timeAxis.resolution;
  }
  // Calculates the time column width/height based on the value defined viewPreset "tickWidth/tickHeight". It also
  // checks for the forceFit view option and the snap, both of which impose constraints on the time column width
  // configuration.
  calculateTickSize(proposedSize) {
    const me = this, { forceFit, timeAxis, suppressFit } = me, timelineUnit = timeAxis.unit;
    let size = 0, ratio = 1;
    if (me.snap) {
      const resolution = timeAxis.resolution;
      ratio = DateHelper.getUnitToBaseUnitRatio(timelineUnit, resolution.unit) * resolution.increment;
    }
    if (!suppressFit) {
      const fittingSize = me.availableSpace / timeAxis.visibleTickTimeSpan;
      size = forceFit || proposedSize < fittingSize ? fittingSize : proposedSize;
      if (ratio > 0 && (!forceFit || ratio < 1)) {
        size = Math.max(1, ratio * size) / ratio;
      }
    } else {
      size = proposedSize;
    }
    return size;
  }
  /**
   * Returns the total width/height of the time axis representation, depending on mode.
   * @returns {Number} The width or height
   * @internal
   * @readonly
   */
  get totalSize() {
    return this._totalSize || (this._totalSize = Math.floor(this.tickSize * this.timeAxis.visibleTickTimeSpan));
  }
  /**
   * Get/set the available space for the time axis representation. If size changes it will cause it to update its
   * contents and fire the {@link #event-update} event.
   * @internal
   * @property {Number}
   */
  get availableSpace() {
    return this._availableSpace;
  }
  set availableSpace(space) {
    const me = this, oldAvailableSpace = me._availableSpace;
    me._availableSpace = Math.max(0, space);
    if (me._availableSpace > 0) {
      const newTickSize = me.calculateTickSize(me.originalTickSize);
      if (newTickSize > 0 && (newTickSize !== me.tickSize || oldAvailableSpace === 0)) {
        me.update();
      }
    }
  }
  //endregion
  //region Fitting & snapping
  /**
   * Returns start dates for ticks at the specified level in format { date, isMajor }.
   * @param {Number} level Level in headers array, `0` meaning the topmost...
   * @param {Boolean} useLowestHeader Use lowest level
   * @param getEnd
   * @returns {Array}
   * @internal
   */
  getDates(level = this.columnLinesFor, useLowestHeader = false, getEnd = false) {
    const me = this, ticks = [], linesForLevel = useLowestHeader ? me.lowestHeader : level, majorLevel = me.majorHeaderLevel, levelUnit = me.headers && me.headers[level].unit, majorUnit = majorLevel != null && me.headers && me.headers[majorLevel].unit, validMajor = majorLevel != null && DateHelper.doesUnitsAlign(majorUnit, levelUnit), hasGenerator = Boolean(me.headers && me.headers[linesForLevel].cellGenerator);
    if (hasGenerator) {
      const cells = me.columnConfig[linesForLevel];
      for (let i = 1, l = cells.length; i < l; i++) {
        ticks.push({ date: cells[i].startDate });
      }
    } else {
      me.forEachInterval(linesForLevel, (start, end) => {
        ticks.push({
          date: getEnd ? end : start,
          // do not want to consider tick to be major tick, hence the check for majorHeaderLevel
          isMajor: majorLevel !== level && validMajor && me.isMajorTick(getEnd ? end : start)
        });
      });
    }
    return ticks;
  }
  get forceFit() {
    return this._forceFit;
  }
  /**
   * This function fits the time columns into the available space in the time axis column.
   * @param {Boolean} suppressEvent `true` to skip firing the 'update' event.
   * @internal
   */
  fitToAvailableSpace(suppressEvent) {
    const proposedSize = Math.floor(this.availableSpace / this.timeAxis.visibleTickTimeSpan);
    this.setTickSize(proposedSize, suppressEvent);
  }
  get snap() {
    return this._snap;
  }
  /**
   * Gets/sets the snap value for the model. Setting it will cause it to update its contents and fire the
   * {@link #event-update} event.
   * @property {Boolean}
   * @internal
   */
  set snap(value2) {
    if (value2 !== this._snap) {
      this._snap = value2;
      if (this.configured) {
        this.update();
      }
    }
  }
  //endregion
  //region Headers
  // private
  createHeaderRow(position, headerRowConfig, headerCells) {
    const me = this, cells = [], { align, headerCellCls = "" } = headerRowConfig, today = DateHelper.clearTime(/* @__PURE__ */ new Date()), { timeAxis } = me, tickLevel = me.headers.length - 1, createCellContext = (start, end, i, isLast, data) => {
      let value2 = DateHelper.format(start, headerRowConfig.dateFormat);
      const isInteriorTick = i > 0 && !isLast, cellData = {
        align,
        start,
        end,
        value: data ? data.header : value2,
        headerCellCls,
        width: tickLevel === position && me.owner && (timeAxis.fullTicks || isInteriorTick) ? me.owner.tickSize : me.getDistanceBetweenDates(start, end),
        index: i
      };
      if (cellData.width === 0) {
        return;
      }
      cellData.coord = size - 1;
      size += cellData.width;
      me.headersDatesCache[position][start.getTime()] = 1;
      if (headerRowConfig.renderer) {
        value2 = headerRowConfig.renderer.call(headerRowConfig.thisObj || me, start, end, cellData, i, me.owner);
        cellData.value = value2 == null ? "" : value2;
      }
      if (headerRowConfig.unit === "day" && (!headerRowConfig.increment || headerRowConfig.increment === 1)) {
        cellData.headerCellCls += " b-sch-dayheadercell-" + start.getDay();
        if (DateHelper.clearTime(start, true) - today === 0) {
          cellData.headerCellCls += " b-sch-dayheadercell-today";
        }
      }
      cells.push(cellData);
    };
    let size = 0;
    me.headersDatesCache[position] = {};
    if (headerCells) {
      headerCells.forEach((cellData, i) => createCellContext(cellData.start, cellData.end, i, i === headerCells.length - 1, cellData));
    } else {
      me.forEachInterval(position, createCellContext);
    }
    return cells;
  }
  get mainHeader() {
    return "mainHeaderLevel" in this ? this.headers[this.mainHeaderLevel] : this.bottomHeader;
  }
  get bottomHeader() {
    return this.headers[this.headers.length - 1];
  }
  get lowestHeader() {
    return this.headers.length - 1;
  }
  /**
   * This method is meant to return the level of the header which 2nd lowest.
   * It is used for {@link #function-isMajorTick} method
   * @returns {String}
   * @private
   */
  get majorHeaderLevel() {
    const { headers } = this;
    if (headers) {
      return Math.max(headers.length - 2, 0);
    }
    return null;
  }
  //endregion
  //region Ticks
  /**
   * For vertical view (and column lines plugin) we sometimes want to know if current tick starts along with the
   * upper header level.
   * @param {Date} date
   * @returns {Boolean}
   * @private
   */
  isMajorTick(date2) {
    const nextLevel = this.majorHeaderLevel;
    return nextLevel != null && this.headersDatesCache[nextLevel] && this.headersDatesCache[nextLevel][date2.getTime()] || false;
  }
  /**
   * Calls the supplied iterator function once per interval. The function will be called with three parameters, start date and end date and an index.
   * Return false to break the iteration.
   * @param {Number} position The index of the header in the headers array.
   * @param {Function} iteratorFn The function to call, will be called with start date, end date and "tick index"
   * @param {Object} [thisObj] `this` reference for the function
   * @internal
   */
  forEachInterval(position, iteratorFn, thisObj = this) {
    const { headers, timeAxis } = this;
    if (headers) {
      if (position === headers.length - 1) {
        timeAxis.forEach(
          (r, index) => iteratorFn.call(thisObj, r.startDate, r.endDate, index, index === timeAxis.count - 1)
        );
      } else {
        const header = headers[position];
        timeAxis.forEachAuxInterval(header.unit, header.increment, iteratorFn, thisObj);
      }
    }
  }
  /**
   * Calls the supplied iterator function once per interval. The function will be called with three parameters, start date and end date and an index.
   * Return false to break the iteration.
   * @internal
   * @param {Function} iteratorFn The function to call
   * @param {Object} [thisObj] `this` reference for the function
   */
  forEachMainInterval(iteratorFn, thisObj) {
    this.forEachInterval(this.mainHeaderLevel, iteratorFn, thisObj);
  }
  //endregion
  //region ViewPreset
  consumeViewPreset(preset) {
    const me = this;
    me.headers = null;
    me.getConfig("tickSize");
    me.viewPreset = preset;
    Object.assign(me, {
      headers: preset.headers,
      columnLinesFor: preset.columnLinesFor,
      mainHeaderLevel: preset.mainHeaderLevel,
      _tickSize: me.isHorizontal ? preset.tickWidth : preset.tickHeight
    });
    me.originalTickSize = me.tickSize;
  }
  //endregion
};
TimeAxisViewModel._$name = "TimeAxisViewModel";

// ../Scheduler/lib/Scheduler/view/mixin/TimelineDateMapper.js
var tempDate2 = /* @__PURE__ */ new Date();
var TimelineDateMapper_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    //region Coordinate <-> Date
    getRtlX(x) {
      if (this.rtl && this.isHorizontal) {
        x = this.timeAxisViewModel.totalSize - x;
      }
      return x;
    }
    /**
     * Gets the date for an X or Y coordinate, either local to the view element or the page based on the 3rd argument.
     * If the coordinate is not in the currently rendered view, null will be returned unless the `allowOutOfRange`
     * parameter is passed a `true`.
     * @param {Number} coordinate The X or Y coordinate
     * @param {'floor'|'round'|'ceil'} [roundingMethod] Rounding method to use. 'floor' to take the tick (lowest header
     * in a time axis) start date, 'round' to round the value to nearest increment or 'ceil' to take the tick end date
     * @param {Boolean} [local] true if the coordinate is local to the scheduler view element
     * @param {Boolean} [allowOutOfRange] By default, this returns `null` if the position is outside
     * of the time axis. Pass `true` to attempt to calculate a date outside of the time axis.
     * @returns {Date} The Date corresponding to the X or Y coordinate
     * @category Dates
     */
    getDateFromCoordinate(coordinate, roundingMethod, local = true, allowOutOfRange = false, ignoreRTL = false) {
      if (!local) {
        coordinate = this.currentOrientation.translateToScheduleCoordinate(coordinate);
      }
      if (!ignoreRTL) {
        coordinate = this.getRtlX(coordinate);
      }
      return this.timeAxisViewModel.getDateFromPosition(coordinate, roundingMethod, allowOutOfRange);
    }
    getDateFromCoord(options) {
      return this.getDateFromCoordinate(options.coord, options.roundingMethod, options.local, options.allowOutOfRange, options.ignoreRTL);
    }
    // Same as above, but ignoresRTL (for example for when positioning using inset)
    getDateFromVirtualCoord(options) {
      return this.getDateFromCoordinate(options.coord, options.roundingMethod, options.local, options.allowOutOfRange, true);
    }
    /**
     * Gets the date for an XY coordinate regardless of the orientation of the time axis.
     * @param {Array} xy The page X and Y coordinates
     * @param {'floor'|'round'|'ceil'} [roundingMethod] Rounding method to use. 'floor' to take the tick (lowest header
     * in a time axis) start date, 'round' to round the value to nearest increment or 'ceil' to take the tick end date
     * @param {Boolean} [local] true if the coordinate is local to the scheduler element
     * @param {Boolean} [allowOutOfRange] By default, this returns `null` if the position is outside
     * of the time axis. Pass `true` to attempt to calculate a date outside of the time axis.
     * @returns {Date} the Date corresponding to the xy coordinate
     * @category Dates
     */
    getDateFromXY(xy2, roundingMethod, local = true, allowOutOfRange = false) {
      return this.currentOrientation.getDateFromXY(xy2, roundingMethod, local, allowOutOfRange);
    }
    /**
     * Gets the time for a DOM event such as 'mousemove' or 'click' regardless of the orientation of the time axis.
     * @param {Event} e the Event instance
     * @param {'floor'|'round'|'ceil'} [roundingMethod] Rounding method to use. 'floor' to take the tick (lowest header
     * in a time axis) start date, 'round' to round the value to nearest increment or 'ceil' to take the tick end date
     * @param {Boolean} [allowOutOfRange] By default, this returns `null` if the position is outside
     * of the time axis. Pass `true` to attempt to calculate a date outside of the time axis.
     * @returns {Date} The date corresponding to the EventObject's position along the orientation of the time axis.
     * @category Dates
     */
    getDateFromDomEvent(e, roundingMethod, allowOutOfRange = false) {
      return this.getDateFromXY([e.pageX, e.pageY], roundingMethod, false, allowOutOfRange);
    }
    /**
     * Gets the start and end dates for an element Region
     * @param {Core.helper.util.Rectangle} rect The rectangle to map to start and end dates
     * @param {'floor'|'round'|'ceil'} roundingMethod Rounding method to use. 'floor' to take the tick (lowest header
     * in a time axis) start date, 'round' to round the value to nearest increment or 'ceil' to take the tick end date
     * @param {Number} duration The duration in MS of the underlying event
     * @returns {Object} an object containing start/end properties
     */
    getStartEndDatesFromRectangle(rect, roundingMethod, duration, allowOutOfRange = false, local = true) {
      const me = this, { isHorizontal } = me, startPos = isHorizontal ? rect.x : rect.top, endPos = isHorizontal ? rect.right : rect.bottom;
      let start, end;
      if (startPos >= 0 && endPos < me.timeAxisViewModel.totalSize) {
        start = me.getDateFromCoordinate(startPos, roundingMethod, local);
        end = me.getDateFromCoordinate(endPos, roundingMethod, local);
      } else if (startPos < 0) {
        end = me.getDateFromCoordinate(endPos, roundingMethod, local, allowOutOfRange);
        start = end && DateHelper.add(end, -duration, "ms");
      } else {
        start = me.getDateFromCoordinate(startPos, roundingMethod, local, allowOutOfRange);
        end = start && DateHelper.add(start, duration, "ms");
      }
      return {
        start,
        end
      };
    }
    //endregion
    //region Date display
    /**
     * Method to get a displayed end date value, see {@link #function-getFormattedEndDate} for more info.
     * @private
     * @param {Date} endDate The date to format
     * @param {Date} startDate The start date
     * @returns {Date} The date value to display
     */
    getDisplayEndDate(endDate, startDate) {
      if (
        // If time is midnight,
        endDate.getHours() === 0 && endDate.getMinutes() === 0 && // and end date is greater then start date
        (!startDate || !(endDate.getYear() === startDate.getYear() && endDate.getMonth() === startDate.getMonth() && endDate.getDate() === startDate.getDate())) && // and UI display format doesn't contain hour info (in this case we'll just display the exact date)
        !DateHelper.formatContainsHourInfo(this.displayDateFormat)
      ) {
        endDate = DateHelper.add(endDate, -1, "day");
      }
      return endDate;
    }
    /**
     * Method to get a formatted end date for a scheduled event, the grid uses the "displayDateFormat" property defined in the current view preset.
     * End dates are formatted as 'inclusive', meaning when an end date falls on midnight and the date format doesn't involve any hour/minute information,
     * 1ms will be subtracted (e.g. 2010-01-08T00:00:00 will first be modified to 2010-01-07 before being formatted).
     * @private
     * @param {Date} endDate The date to format
     * @param {Date} startDate The start date
     * @returns {String} The formatted date
     */
    getFormattedEndDate(endDate, startDate) {
      return this.getFormattedDate(this.getDisplayEndDate(endDate, startDate));
    }
    //endregion
    //region Other date functions
    /**
     * Gets the x or y coordinate relative to the scheduler element, or page coordinate (based on the 'local' flag)
     * If the coordinate is not in the currently rendered view, -1 will be returned.
     * @param {Date|Number} date the date to query for (or a date as ms)
     * @param {Boolean|Object} options `true` to return a coordinate local to the scheduler view element (defaults to true),
     * @param {Boolean} options.local `true` if coordinate should be local to the time axis, or false to be a page
     * coordinate
     * @returns {Number} the x or y position representing the date on the time axis
     * @category Dates
     */
    getCoordinateFromDate(date2, options = true) {
      if (!date2) {
        throw new Error("Invalid date passed to getCoordinateFromDate");
      }
      const me = this, { timeAxisViewModel } = me, {
        isContinuous,
        startMS,
        endMS,
        startDate,
        endDate,
        unit
      } = me.timeAxis, dateMS = date2.valueOf();
      if (options === true) {
        options = {
          local: true
        };
      } else if (!options) {
        options = {
          local: false
        };
      } else if (!("local" in options)) {
        options.local = true;
      }
      let pos;
      if (!(date2 instanceof Date)) {
        tempDate2.setTime(date2);
        date2 = tempDate2;
      }
      if (isContinuous && date2.getTimezoneOffset() === startDate.getTimezoneOffset() && startDate.getTimezoneOffset() === endDate.getTimezoneOffset() && DateHelper.getUnitToBaseUnitRatio(unit, "day") !== -1) {
        if (dateMS < startMS || dateMS > endMS) {
          return -1;
        }
        pos = (dateMS - startMS) / (endMS - startMS) * timeAxisViewModel.totalSize;
      } else {
        pos = timeAxisViewModel.getPositionFromDate(date2, options);
      }
      if (me.rtl && me.isHorizontal && !(options == null ? void 0 : options.ignoreRTL)) {
        pos = timeAxisViewModel.totalSize - pos;
      }
      if (!options.local) {
        pos = me.currentOrientation.translateToPageCoordinate(pos);
      }
      return pos;
    }
    getVirtualCoordFromDate(date2, options) {
      return this.getCoordinateFromDate(date2, { ...options, ignoreRTL: true });
    }
    /**
     * Returns the distance in pixels for the time span in the view.
     * @param {Date} startDate The start date of the span
     * @param {Date} endDate The end date of the span
     * @returns {Number} The distance in pixels
     * @category Dates
     */
    getTimeSpanDistance(startDate, endDate) {
      return this.timeAxisViewModel.getDistanceBetweenDates(startDate, endDate);
    }
    /**
     * Returns the center date of the currently visible timespan of scheduler.
     *
     * @property {Date}
     * @readonly
     * @category Dates
     */
    get viewportCenterDate() {
      if (this.isVisible) {
        const { timeAxis, timelineScroller } = this;
        if (timeAxis.isContinuous) {
          const timeAxisOffset = (timelineScroller.position + timelineScroller.clientSize / 2) / timelineScroller.scrollSize;
          return new Date(timeAxis.startMS + (timeAxis.endMS - timeAxis.startMS) * timeAxisOffset);
        }
        return this.getDateFromCoord({
          coord: timelineScroller.position + timelineScroller.clientSize / 2,
          allowOutOfRange: this.infiniteScroll
        });
      }
    }
    get viewportCenterDateCached() {
      return this.cachedCenterDate || (this.cachedCenterDate = this.viewportCenterDate);
    }
    //endregion
    //region TimeAxis getters/setters
    /**
     * Gets/sets the current time resolution object, which contains a unit identifier and an increment count
     * `{ unit, increment }`. This value means minimal task duration you can create using UI.
     *
     * For example when you drag create a task or drag & drop a task, if increment is 5 and unit is 'minute'
     * that means that you can create tasks in 5 minute increments, or move it in 5 minute steps.
     *
     * This value is taken from viewPreset {@link Scheduler.preset.ViewPreset#field-timeResolution timeResolution}
     * config by default. When supplying a `Number` to the setter only the `increment` is changed and the `unit` value
     * remains untouched.
     *
     * ```javascript
     * timeResolution : {
     *   unit      : 'minute',  //Valid values are "millisecond", "second", "minute", "hour", "day", "week", "month", "quarter", "year".
     *   increment : 5
     * }
     * ```
     *
     * <div class="note">When the {@link Scheduler/view/mixin/TimelineEventRendering#config-fillTicks} option is
     * enabled, the resolution will be in full ticks regardless of configured value.</div>
     *
     * @prp {Object|Number}
     * @category Dates
     */
    get timeResolution() {
      return this.timeAxis.resolution;
    }
    set timeResolution(resolution) {
      this.timeAxis.resolution = typeof resolution === "number" ? {
        increment: resolution,
        unit: this.timeAxis.resolution.unit
      } : resolution;
    }
    //endregion
    //region Snap
    get snap() {
      var _a6, _b2;
      return (_b2 = (_a6 = this._timeAxisViewModel) == null ? void 0 : _a6.snap) != null ? _b2 : this._snap;
    }
    updateSnap(snap) {
      if (!this.isConfiguring) {
        this.timeAxisViewModel.snap = snap;
        this.timeAxis.forceFullTicks = snap && this.fillTicks;
      }
    }
    //endregion
    onSchedulerHorizontalScroll(subGrid, scrollLeft, scrollX, scrollingToCenter) {
      if (!scrollingToCenter) {
        this.cachedCenterDate = null;
      }
    }
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "TimelineDateMapper"), __publicField(_a5, "configurable", {
    /**
     * Set to `true` to snap to the current time resolution increment while interacting with scheduled events.
     *
     * The time resolution increment is either determined by the currently applied view preset, or it can be
     * overridden using {@link #property-timeResolution}.
     *
     * If you also want to snap to resources while dragging events, set the {@link Scheduler/feature/EventDrag#config-snapToResource} config of the EventDrag feature to `true`.
     *
     * <div class="note">When the {@link Scheduler/view/mixin/TimelineEventRendering#config-fillTicks} option is
     * enabled, snapping will align to full ticks, regardless of the time resolution.</div>
     *
     * @prp {Boolean}
     * @default
     * @category Scheduled events
     */
    snap: false
  }), _a5;
};

// ../Scheduler/lib/Scheduler/view/mixin/TimelineDomEvents.js
var { eventNameMap } = EventHelper;
var TimelineDomEvents_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    constructor() {
      super(...arguments);
      // Currently hovered events (can be parent + child)
      __publicField(this, "hoveredEvents", /* @__PURE__ */ new Set());
    }
    //endregion
    //region Init
    /**
     * Adds listeners for DOM events for the scheduler and its events.
     * Which events is specified in Scheduler#schedulerEvents.
     * @private
     */
    initDomEvents() {
      const me = this, { schedulerEvents } = me;
      schedulerEvents.element = me.timeAxisSubGridElement;
      schedulerEvents.thisObj = me;
      EventHelper.on(schedulerEvents);
      EventHelper.on({
        element: me.timeAxisSubGridElement,
        mouseleave: "handleScheduleLeaveEvent",
        capture: true,
        thisObj: me
      });
      if (me.updateTimelineContextOnScroll && BrowserHelper.supportsPointerEventConstructor) {
        EventHelper.on({
          element: document,
          scroll: "onScheduleScroll",
          capture: true,
          thisObj: me
        });
      }
    }
    //endregion
    //region Event handling
    getTimeSpanMouseEventParams(eventElement, event) {
      throw new Error("Implement in subclass");
    }
    getScheduleMouseEventParams(cellData, event) {
      throw new Error("Implement in subclass");
    }
    /**
     * Wraps dom Events for the scheduler and event bars and fires as our events.
     * For example click -> scheduleClick or eventClick
     * @private
     * @param event
     */
    handleScheduleEvent(event) {
      const me = this;
      if (me.ignoreDomEventsWhileScrolling && (me.scrolling || me.timeAxisSubGrid.scrolling)) {
        return;
      }
      const timelineContext = me.getTimelineEventContext(event);
      me.lastPointerEvent = event;
      if (timelineContext) {
        me.trigger(`${timelineContext.eventElement ? me.scheduledEventName : "schedule"}${eventNameMap[timelineContext.domEvent.type] || StringHelper.capitalize(timelineContext.domEvent.type)}`, timelineContext);
      }
      me.timelineContext = timelineContext;
    }
    handleScheduleLeaveEvent(event) {
      if (event.target === this.timeAxisSubGridElement) {
        this.handleScheduleEvent(event);
      }
    }
    /**
     * This handles the scheduler being scrolled below the mouse by trackpad or keyboard events.
     * The context, if present needs to be recalculated.
     * @private
     */
    onScheduleScroll({ target }) {
      var _a6, _b2, _c2, _d, _e;
      const me = this;
      if (target && me.updateTimelineContextOnScroll && !((_a6 = me.features.pan) == null ? void 0 : _a6.isActive) && !me.partners.some((p) => {
        var _a7;
        return (_a7 = p.features.pan) == null ? void 0 : _a7.isActive;
      }) && (target.contains(me.element) || me.bodyElement.contains(target))) {
        const { timelineContext, lastPointerEvent } = me;
        if (timelineContext) {
          const targetElement = DomHelper.elementFromPoint(timelineContext.domEvent.clientX, timelineContext.domEvent.clientY), pointerEvent = new BrowserHelper.PointerEventConstructor("pointermove", lastPointerEvent), mouseEvent = new MouseEvent("mousemove", lastPointerEvent);
          Object.defineProperty(pointerEvent, "pointerId", {
            value: (_e = (_d = (_b2 = GlobalEvents_default.currentPointerDown) == null ? void 0 : _b2.pointerId) != null ? _d : (_c2 = GlobalEvents_default.currentTouch) == null ? void 0 : _c2.identifier) != null ? _e : 1
          });
          pointerEvent.scrollInitiated = mouseEvent.scrollInitiated = true;
          targetElement == null ? void 0 : targetElement.dispatchEvent(pointerEvent);
          targetElement == null ? void 0 : targetElement.dispatchEvent(mouseEvent);
        }
      }
    }
    updateTimelineContext(context, oldContext) {
      this.trigger("timelineContextChange", { oldContext, context });
      if (context && !oldContext) {
        this.trigger("scheduleMouseEnter", context);
      } else if (!context) {
        this.trigger("scheduleMouseLeave", { event: oldContext.event });
      }
    }
    /**
     * Gathers contextual information about the schedule contextual position of the passed event.
     *
     * Used by schedule mouse event handlers, but also by the scheduleContext feature.
     * @param {Event} domEvent The DOM event to gather context for.
     * @returns {TimelineContext} the schedule DOM event context
     * @internal
     */
    getTimelineEventContext(domEvent) {
      var _a6, _b2;
      const me = this, { scheduleContext } = me.features;
      if (scheduleContext) {
        const { context } = scheduleContext, { type } = domEvent, t = type.endsWith("leave") || type.endsWith("out") ? domEvent.relatedTarget : domEvent.target;
        if (context && (((_a6 = scheduleContext.widget) == null ? void 0 : _a6.owns(t)) || scheduleContext.element.contains(t))) {
          scheduleContext.element.style.pointerEvents = "none";
          context.eventElement = (_b2 = document.elementFromPoint(domEvent.clientX, domEvent.clientY)) == null ? void 0 : _b2.closest(me.eventSelector);
          context.eventRecord = me.resolveEventRecord(context.eventElement);
          scheduleContext.element.style.pointerEvents = "";
          me._timelineContext = null;
          return context;
        }
      }
      const eventElement = domEvent.target.closest(me.eventInnerSelector), cellElement = me.getCellElementFromDomEvent(domEvent);
      if (cellElement) {
        const time = me.getDateFromDomEvent(domEvent);
        if (!time) {
          return;
        }
        const date2 = me.timeAxis.floorDate(time), cellData = DomDataStore.get(cellElement), mouseParams = eventElement ? me.getTimeSpanMouseEventParams(eventElement, domEvent) : me.getScheduleMouseEventParams(cellData, domEvent);
        if (!mouseParams) {
          return;
        }
        const index = me.isVertical ? me.resourceStore.indexOf(mouseParams.resourceRecord) : cellData.row.dataIndex, tickIndex = me.timeAxis.getTickFromDate(date2), tick = me.timeAxis.getAt(Math.floor(tickIndex));
        if (tick) {
          return {
            scheduler: me,
            isTimelineContext: true,
            domEvent,
            eventElement,
            cellElement,
            index,
            tick,
            tickIndex,
            date: date2,
            time,
            tickStartDate: tick.startDate,
            tickEndDate: tick.endDate,
            tickParentIndex: tick.parentIndex,
            row: cellData.row,
            event: domEvent,
            ...mouseParams
          };
        }
      }
    }
    getCellElementFromDomEvent({ target, clientY, type }) {
      var _a6;
      const me = this, {
        isVertical,
        foregroundCanvas
      } = me, eventElement = target.closest(me.eventSelector);
      if (isVertical) {
        return me.timeAxisSubGridElement.querySelector(me.timeCellSelector);
      }
      if (eventElement) {
        return me.getCell({
          record: me.resolveRowRecord(eventElement) || me.store.getAt(me.rowManager.getRowAt(clientY, false).dataIndex),
          column: me.timeAxisColumn
        });
      }
      if (foregroundCanvas.contains(target)) {
        if (target === foregroundCanvas || target.closest(".b-sch-resourcetimerange") || type === "mousemove") {
          return (_a6 = me.rowManager.getRowAt(clientY, false)) == null ? void 0 : _a6.getCell(me.timeAxisColumn.id);
        }
      }
      return target.matches(".b-grid-row") ? target.firstElementChild : target.closest(me.timeCellSelector);
    }
    // Overridden by ResourceTimeRanges to "pass events through" to the schedule
    matchScheduleCell(element) {
      return element.closest(this.timeCellSelector);
    }
    onElementMouseButtonEvent(event) {
      const targetCell = event.target.closest(".b-sch-header-timeaxis-cell");
      if (targetCell) {
        const me = this, position = targetCell.parentElement.dataset.headerPosition, headerCells = me.timeAxisViewModel.columnConfig[position], index = me.timeAxis.isFiltered ? headerCells.findIndex((cell) => cell.index == targetCell.dataset.tickIndex) : targetCell.dataset.tickIndex, cellConfig = headerCells[index], contextMenu = me.features.contextMenu;
        if (!contextMenu || event.type !== contextMenu.triggerEvent) {
          this.trigger(`timeAxisHeader${StringHelper.capitalize(event.type)}`, {
            startDate: cellConfig.start,
            endDate: cellConfig.end,
            event
          });
        }
      }
    }
    onElementMouseDown(event) {
      this.onElementMouseButtonEvent(event);
      super.onElementMouseDown(event);
    }
    onElementClick(event) {
      this.onElementMouseButtonEvent(event);
      super.onElementClick(event);
    }
    onElementDblClick(event) {
      this.onElementMouseButtonEvent(event);
      super.onElementDblClick(event);
    }
    onElementContextMenu(event) {
      this.onElementMouseButtonEvent(event);
      super.onElementContextMenu(event);
    }
    /**
     * Relays mouseover events as eventmouseenter if over rendered event.
     * Also adds Scheduler#overScheduledEventClass to the hovered element.
     * @private
     */
    onElementMouseOver(event) {
      var _a6;
      const me = this;
      if (me.ignoreDomEventsWhileScrolling && (me.scrolling || me.timeAxisSubGrid.scrolling)) {
        return;
      }
      super.onElementMouseOver(event);
      const { target } = event, { hoveredEvents } = me;
      if ((target.closest(me.eventInnerSelector) || target.matches(".b-sch-terminal-hover-area")) && !((_a6 = me.features.eventDrag) == null ? void 0 : _a6.isDragging)) {
        const eventElement = target.closest(me.eventSelector);
        if (!hoveredEvents.has(eventElement) && !me.preventOverCls) {
          hoveredEvents.add(eventElement);
          eventElement.classList.add(me.overScheduledEventClass);
          const params = me.getTimeSpanMouseEventParams(eventElement, event);
          if (params) {
            me.trigger(`${me.scheduledEventName}MouseEnter`, params);
          }
        }
      } else if (hoveredEvents.size) {
        me.unhoverAll(event);
      }
    }
    /**
     * Relays mouseout events as eventmouseleave if out from rendered event.
     * Also removes Scheduler#overScheduledEventClass from the hovered element.
     * @private
     */
    onElementMouseOut(event) {
      var _a6;
      const me = this, { features } = me, { target, relatedTarget } = event;
      if (!(relatedTarget == null ? void 0 : relatedTarget.closest(".b-sch-canvas *"))) {
        super.onElementMouseOut(event);
      }
      const eventWrap = target.closest(me.eventSelector), eventInner = eventWrap == null ? void 0 : eventWrap.querySelector(me.eventInnerSelector), timeSpanRecord = eventWrap && me.resolveTimeSpanRecord(eventWrap);
      if (eventInner && timeSpanRecord && me.hoveredEvents.has(eventWrap) && !((_a6 = features.eventDrag) == null ? void 0 : _a6.isDragging)) {
        if (relatedTarget && (DomHelper.isDescendant(eventInner, relatedTarget) || relatedTarget.matches(".b-sch-terminal-hover-area"))) {
          return;
        }
      }
      if (eventWrap && me.hoveredEvents.size) {
        me.unhover(eventWrap, event);
      }
    }
    unhover(element, event) {
      const me = this;
      element.classList.remove(me.overScheduledEventClass);
      const params = me.getTimeSpanMouseEventParams(element, event);
      if (params) {
        me.trigger(`${me.scheduledEventName}MouseLeave`, me.getTimeSpanMouseEventParams(element, event));
      }
      me.hoveredEvents.delete(element);
    }
    unhoverAll(event) {
      for (const element of this.hoveredEvents) {
        !element.isReleased && !element.classList.contains("b-released") && this.unhover(element, event);
      }
      this.hoveredEvents.clear();
    }
    //endregion
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, /**
   * Fires after a click on a time axis cell
   * @event timeAxisHeaderClick
   * @param {Scheduler.column.TimeAxisColumn|Scheduler.column.VerticalTimeAxisColumn} source The column object
   * @param {Date} startDate The start date of the header cell
   * @param {Date} endDate The end date of the header cell
   * @param {Event} event The event object
   */
  /**
   * Fires after a double click on a time axis cell
   * @event timeAxisHeaderDblClick
   * @param {Scheduler.column.TimeAxisColumn|Scheduler.column.VerticalTimeAxisColumn} source The column object
   * @param {Date} startDate The start date of the header cell
   * @param {Date} endDate The end date of the header cell
   * @param {Event} event The event object
   */
  /**
   * Fires after a right click on a time axis cell
   * @event timeAxisHeaderContextMenu
   * @param {Scheduler.column.TimeAxisColumn|Scheduler.column.VerticalTimeAxisColumn} source The column object
   * @param {Date} startDate The start date of the header cell
   * @param {Date} endDate The end date of the header cell
   * @param {Event} event The event object
   */
  __publicField(_a5, "$name", "TimelineDomEvents"), //region Default config
  __publicField(_a5, "configurable", {
    /**
     * The currently hovered timeline context. This is updated as the mouse or pointer moves over the timeline.
     * @member {TimelineContext} timelineContext
     * @readonly
     * @category Dates
     */
    timelineContext: {
      $config: {
        // Reject non-changes so that when set from scheduleMouseMove and EventMouseMove,
        // we only update the context and fire events when it changes.
        equal(c1, c2) {
          return (c1 == null ? void 0 : c1.index) === (c2 == null ? void 0 : c2.index) && (c1 == null ? void 0 : c1.tickParentIndex) === (c2 == null ? void 0 : c2.tickParentIndex) && !(((c1 == null ? void 0 : c1.tickStartDate) || 0) - ((c2 == null ? void 0 : c2.tickStartDate) || 0)) && (c1 == null ? void 0 : c1.eventRecord) === (c2 == null ? void 0 : c2.eventRecord);
        }
      }
    },
    updateTimelineContextOnScroll: false,
    /**
     * Set to `true` to ignore reacting to DOM events (mouseover/mouseout etc) while scrolling. Useful if you
     * want to maximize scroll performance.
     * @config {Boolean}
     * @default false
     */
    ignoreDomEventsWhileScrolling: null
  }), __publicField(_a5, "properties", {
    schedulerEvents: {
      pointermove: "handleScheduleEvent",
      mouseover: "handleScheduleEvent",
      mousedown: "handleScheduleEvent",
      mouseup: "handleScheduleEvent",
      click: "handleScheduleEvent",
      dblclick: "handleScheduleEvent",
      contextmenu: "handleScheduleEvent",
      mousemove: "handleScheduleEvent",
      mouseout: "handleScheduleEvent"
    }
  }), __publicField(_a5, "delayable", {
    // Allow the scroll event to complete in its thread, and dispatch the mousemove event next AF
    onScheduleScroll: "raf"
  }), _a5;
};

// ../Scheduler/lib/Scheduler/view/mixin/TimelineViewPresets.js
var TimelineViewPresets_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    //region Default config
    static get configurable() {
      return {
        /**
         * A string key used to lookup a predefined {@link Scheduler.preset.ViewPreset} (e.g. 'weekAndDay', 'hourAndDay'),
         * managed by {@link Scheduler.preset.PresetManager}. See {@link Scheduler.preset.PresetManager} for more information.
         * Or a config object for a viewPreset.
         *
         * Options:
         * - 'secondAndMinute'
         * - 'minuteAndHour'
         * - 'hourAndDay'
         * - 'dayAndWeek'
         * - 'dayAndMonth'
         * - 'weekAndDay'
         * - 'weekAndMonth',
         * - 'monthAndYear'
         * - 'year'
         * - 'manyYears'
         * - 'weekAndDayLetter'
         * - 'weekDateAndMonth'
         * - 'day'
         * - 'week'
         *
         * If passed as a config object, the settings from the viewPreset with the provided `base` property will be used along
         * with any overridden values in your object.
         *
         * To override:
         * ```javascript
         * viewPreset : {
         *   base    : 'hourAndDay',
         *   id      : 'myHourAndDayPreset',
         *   headers : [
         *       {
         *           unit      : "hour",
         *           increment : 12,
         *           renderer  : (startDate, endDate, headerConfig, cellIdx) => {
         *               return "";
         *           }
         *       }
         *   ]
         * }
         * ```
         * or set a new valid preset config if the preset is not registered in the {@link Scheduler.preset.PresetManager}.
         *
         * When you use scheduler in weekview mode, this config is used to pick view preset. If passed view preset is not
         * supported by weekview (only 2 supported by default - 'day' and 'week') default preset will be used - 'week'.
         * @config {String|ViewPresetConfig}
         * @default
         * @category Common
         */
        viewPreset: "weekAndDayLetter",
        /**
         * Get the {@link Scheduler.preset.PresetStore} created for the Scheduler,
         * or set an array of {@link Scheduler.preset.ViewPreset} config objects.
         * @member {Scheduler.preset.PresetStore|ViewPresetConfig[]} presets
         * @category Common
         */
        /**
         * An array of {@link Scheduler.preset.ViewPreset} config objects
         * which describes the available timeline layouts for this scheduler.
         *
         * By default, a predefined set is loaded from the {@link Scheduler.preset.PresetManager}.
         *
         * A {@link Scheduler.preset.ViewPreset} describes the granularity of the
         * timeline view and the layout and subdivisions of the timeline header.
         * @config {ViewPresetConfig[]} presets
         *
         * @category Common
         */
        presets: true,
        /**
         * Defines how dates will be formatted in tooltips etc. This config has priority over similar config on the
         * view preset. For allowed values see {@link Core.helper.DateHelper#function-format-static}.
         *
         * By default, this is ingested from {@link Scheduler.preset.ViewPreset} upon change of
         * {@link Scheduler.preset.ViewPreset} (Such as when zooming in or out). But Setting this
         * to your own value, overrides that behaviour.
         * @prp {String}
         * @category Scheduled events
         */
        displayDateFormat: null
      };
    }
    //endregion
    /**
     * Get/set the current view preset
     * @member {Scheduler.preset.ViewPreset|ViewPresetConfig|String} viewPreset
     * @param [viewPreset.options]
     * @param {Date} [viewPreset.options.startDate] A new start date for the time axis
     * @param {Date} [viewPreset.options.endDate] A new end date for the time axis
     * @param {Date} [viewPreset.options.centerDate] Where to center the new time axis
     * @param {Number} [viewPreset.options.scrollPosition] The scroll position to scroll
     * the new time axis to. This takes precedence over any date-scrolling.
     * It is used when partnering two timelines which must be scroll-synced.
     * @category Common
    */
    //region Get/set
    changePresets(presets) {
      const config = {
        owner: this,
        // By default includes all presets
        data: presets === true ? pm.data : []
      };
      if (presets !== true) {
        if (Array.isArray(presets)) {
          for (const preset of presets) {
            if (typeof preset === "string") {
              const presetRecord = pm.getById(preset);
              if (presetRecord) {
                config.data.push(presetRecord.data);
              }
            } else {
              config.data.push(preset);
            }
          }
        } else {
          ObjectHelper.assign(config, presets);
        }
      }
      return new PresetStore(config);
    }
    changeViewPreset(viewPreset, oldViewPreset) {
      const me = this, {
        presets
      } = me;
      if (viewPreset) {
        viewPreset = presets.createRecord(viewPreset);
        if (!presets.includes(viewPreset)) {
          presets.add(viewPreset);
        }
      } else {
        viewPreset = presets.first;
      }
      const lastOpts = me.lastViewPresetOptions || {}, options = viewPreset.options || (viewPreset.options = {}), event = {
        ...options,
        from: oldViewPreset,
        to: viewPreset,
        preset: viewPreset
      }, presetChanged = !me._viewPreset || !me._viewPreset.equals(viewPreset);
      delete lastOpts.event;
      if (presetChanged || !ObjectHelper.isEqual(options, lastOpts)) {
        options.event = event;
        if (!presetChanged) {
          me._viewPreset = null;
        }
        if (me.isConfiguring || me.trigger("beforePresetChange", event) !== false) {
          return viewPreset;
        }
      }
    }
    get displayDateFormat() {
      return this._displayDateFormat || this.viewPreset.displayDateFormat;
    }
    updateDisplayDateFormat(format2) {
      this.trigger("displayDateFormatChange", { format: format2 });
    }
    /**
     * Method to get a formatted display date
     * @private
     * @param {Date} date The date
     * @returns {String} The formatted date
     */
    getFormattedDate(date2) {
      return DateHelper.format(date2, this.displayDateFormat);
    }
    updateViewPreset(preset) {
      var _a6;
      const me = this, { options } = preset, {
        event,
        startDate,
        endDate
      } = options, {
        hasTimelineBoundaries,
        isHorizontal,
        _timeAxis: timeAxis,
        // Do not tickle the getter, we are just peeking to see if it's there yet.
        _timeAxisViewModel: timeAxisViewModel
        // Ditto
      } = me, rtl = isHorizontal && me.rtl;
      let {
        centerDate,
        zoomDate,
        zoomPosition
      } = options, forceUpdate = false;
      (_a6 = me.syncSplits) == null ? void 0 : _a6.call(me, (split2) => split2.viewPreset = preset);
      delete preset.options;
      me._viewPresetChanging = true;
      if (timeAxis && !me.isConfiguring) {
        const { timelineScroller } = me;
        me.lastViewPresetOptions = options;
        if (timeAxis.isConfigured) {
          me.suspendRefresh();
          const timeAxisCfg = ObjectHelper.copyProperties({}, me, [
            "weekStartDay",
            "startTime",
            "endTime"
          ]);
          if (me.range && me.calendar && !me.isConfiguring) {
            timeAxisCfg.startDate = me.startDate;
            timeAxisCfg.endDate = me.endDate;
          } else if (me.infiniteScroll) {
            Object.assign(timeAxisCfg, me.calculateInfiniteScrollingDateRange(
              centerDate || (startDate && endDate ? new Date((startDate.getTime() + endDate.getTime()) / 2) : me.viewportCenterDateCached),
              true,
              preset
            ));
          } else if (startDate) {
            timeAxisCfg.startDate = startDate;
            timeAxisCfg.endDate = endDate;
            if (!centerDate && endDate) {
              centerDate = new Date((startDate.getTime() + endDate.getTime()) / 2);
            }
          } else {
            timeAxisCfg.startDate = timeAxis.startDate;
            timeAxisCfg.endDate = endDate || timeAxis.endDate;
            if (!centerDate) {
              centerDate = me.viewportCenterDate;
            }
          }
          timeAxis.isConfigured = false;
          timeAxisCfg.viewPreset = preset;
          timeAxis.reconfigure(timeAxisCfg, true);
          timeAxisViewModel.reconfigure({
            viewPreset: preset,
            headers: preset.headers,
            // This was hardcoded to 'middle' prior to the Preset refactor.
            // In the old code, the default headers were 'top' and 'middle', which
            // meant that 'middle' meant the lowest header.
            // So this is now length - 1.
            columnLinesFor: preset.columnLinesFor != null ? preset.columnLinesFor : preset.headers.length - 1,
            tickSize: options.tickSize || (isHorizontal ? preset.tickWidth : preset.tickHeight || preset.tickWidth || 60)
          });
          if (options.tickSize) {
            me.tickSize = options.tickSize;
          }
          me.resumeRefresh(false);
        }
        me.refresh();
        if (!options.notScroll && me.isPainted) {
          if ("scrollPosition" in options) {
            forceUpdate = timelineScroller.position === options.scrollPosition;
            timelineScroller.scrollTo(options.scrollPosition);
          } else if (options.visibleDate) {
            me.visibleDate = options.visibleDate;
          } else if (zoomDate && zoomPosition) {
            let roundPrecision = 15, unit = "minute";
            if (DateHelper.compareUnits(timeAxis.resolutionUnit, "hour") > 0) {
              roundPrecision = 1;
              unit = "hour";
            }
            const roundPrecisionMs = DateHelper.asMilliseconds(roundPrecision, unit), targetDate = new Date(Math.round(zoomDate / roundPrecisionMs) * roundPrecisionMs);
            event.zoomDate = zoomDate;
            event.zoomPosition = zoomPosition;
            event.zoomLevel = options.zoomLevel;
            if (hasTimelineBoundaries && timelineScroller.position == 0) {
              me.currentOrientation.updateFromHorizontalScroll(0, true, true);
            }
            if (rtl) {
              timelineScroller.position = timelineScroller.scrollWidth - (me.getCoordinateFromDate(targetDate) + zoomPosition);
            } else {
              timelineScroller.position = me.getCoordinateFromDate(targetDate) - zoomPosition;
            }
          } else if (centerDate) {
            me.cachedCenterDate = centerDate;
            event.centerDate = centerDate;
            const viewportSize = me.timelineScroller.clientSize, centerCoord = rtl ? me.timeAxisViewModel.totalSize - me.getCoordinateFromDate(centerDate, true) : me.getCoordinateFromDate(centerDate, true), coord = Math.max(centerCoord - viewportSize / 2, 0);
            if (coord === (me.isHorizontal ? me.scrollLeft : me.scrollTop)) {
              forceUpdate = true;
            } else if (me.isHorizontal) {
              me.scrollHorizontallyTo(coord, { scrollingToCenter: true });
            } else {
              me.scrollVerticallyTo(coord, { scrollingToCenter: true });
            }
          } else {
            if ((me.isHorizontal ? me.scrollLeft : me.scrollTop) === 0) {
              forceUpdate = true;
            } else {
              me.timelineScroller.scrollTo(0);
            }
          }
        }
      }
      me.dataset.presetId = preset.id;
      if (preset.rowHeight && !me.initialConfig.rowHeight) {
        me.rowHeight = preset.rowHeight;
      }
      me.trigger("presetChange", event);
      me._viewPresetChanging = false;
      if (forceUpdate) {
        if (me.isHorizontal) {
          me.currentOrientation.updateFromHorizontalScroll(me.scrollLeft, true);
        } else {
          me.currentOrientation.updateFromVerticalScroll(me.scrollTop);
        }
      }
    }
    //endregion
    doDestroy() {
      if (this._presets.owner === this) {
        this._presets.destroy();
      }
      super.doDestroy();
    }
    // This function is not meant to be called by any code other than Base#getCurrentConfig().
    getCurrentConfig(options) {
      const result = super.getCurrentConfig(options);
      if (result.viewPreset && result.viewPreset.name && !result.viewPreset.base) {
        delete result.viewPreset.name;
      }
      return result;
    }
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "TimelineViewPresets"), _a5;
};

// ../Scheduler/lib/Scheduler/view/mixin/TimelineZoomable.js
var TimelineZoomable_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    constructor() {
      super(...arguments);
      // We cache the last mousewheel position, so that during zooming we can
      // maintain a stable zoom point even if the mouse moves a little.
      __publicField(this, "lastWheelTime", -1);
      __publicField(this, "lastZoomPosition", -1);
    }
    construct(config) {
      const me = this;
      super.construct(config);
      if (me.zoomOnMouseWheel) {
        EventHelper.on({
          element: me.timeAxisSubGridElement,
          wheel: "onWheel",
          // Throttle zooming with the wheel a bit to have greater control of it
          throttled: {
            buffer: 100,
            // Prevent events from slipping through the throttle, causing scroll
            alt: (e) => (e.ctrlKey || e.metaKey) && e.preventDefault()
          },
          thisObj: me,
          capture: true,
          passive: false
        });
      }
      if (me.zoomOnTimeAxisDoubleClick) {
        me.ion({
          timeaxisheaderdblclick: ({ startDate, endDate }) => {
            if (!me.forceFit) {
              me.zoomToSpan({
                startDate,
                endDate
              });
            }
          }
        });
      }
    }
    get maxZoomLevel() {
      return this._maxZoomLevel || this.presets.count - 1;
    }
    /**
     * Get/set the {@link #config-maxZoomLevel} value
     * @property {Number}
     * @category Zoom
     */
    set maxZoomLevel(level) {
      if (typeof level !== "number") {
        level = this.presets.count - 1;
      }
      if (level < 0 || level >= this.presets.count) {
        throw new Error("Invalid range for `maxZoomLevel`");
      }
      this._maxZoomLevel = level;
    }
    get minZoomLevel() {
      return this._minZoomLevel;
    }
    /**
     * Sets the {@link #config-minZoomLevel} value
     * @property {Number}
     * @category Zoom
     */
    set minZoomLevel(level) {
      if (typeof level !== "number") {
        level = 0;
      }
      if (level < 0 || level >= this.presets.count) {
        throw new Error("Invalid range for `minZoomLevel`");
      }
      this._minZoomLevel = level;
    }
    /**
     * Current zoom level, which is equal to the {@link Scheduler.preset.ViewPreset ViewPreset} index
     * in the provided array of {@link Scheduler.view.mixin.TimelineViewPresets#config-presets zoom levels}.
     * @property {Number}
     * @category Zoom
     */
    get zoomLevel() {
      return this.presets.indexOf(this.viewPreset);
    }
    // noinspection JSAnnotator
    set zoomLevel(level) {
      this.zoomToLevel(level);
    }
    /**
     * Returns number of milliseconds per pixel.
     * @param {Object} level Element from array of {@link Scheduler.view.mixin.TimelineViewPresets#config-presets}.
     * @param {Boolean} ignoreActualWidth If true, then density will be calculated using default zoom level settings.
     * Otherwise, density will be calculated for actual tick width.
     * @returns {Number} Return number of milliseconds per pixel.
     * @private
     */
    getMilliSecondsPerPixelForZoomLevel(preset, ignoreActualWidth) {
      const { bottomHeader } = preset, width = this.isHorizontal ? preset.tickWidth : preset.tickHeight;
      return Math.round(
        (DateHelper.add(new Date(1, 0, 1), bottomHeader.increment || 1, bottomHeader.unit) - new Date(1, 0, 1)) / // `actualWidth` is a column width after view adjustments applied to it (see `calculateTickWidth`)
        // we use it if available to return the precise index value from `getCurrentZoomLevelIndex`
        (ignoreActualWidth ? width : preset.actualWidth || width)
      );
    }
    /**
     * Zooms to passed view preset, saving center date. Method accepts config object as a first argument, which can be
     * reduced to primitive type (string,number) when no additional options required. e.g.:
     * ```javascript
     * // zooming to preset
     * scheduler.zoomTo({ preset : 'hourAndDay' })
     * // shorthand
     * scheduler.zoomTo('hourAndDay')
     *
     * // zooming to level
     * scheduler.zoomTo({ level : 0 })
     * // shorthand
     * scheduler.zoomTo(0)
     * ```
     *
     * It is also possible to zoom to a time span by omitting `preset` and `level` configs, in which case scheduler sets
     * the time frame to a specified range and applies zoom level which allows to fit all columns to this range. The
     * given time span will be centered in the scheduling view (unless `centerDate` config provided). In the same time,
     * the start/end date of the whole time axis will be extended to allow scrolling for user.
     * ```javascript
     * // zooming to time span
     * scheduler.zoomTo({
     *     startDate : new Date(..),
     *     endDate : new Date(...)
     * });
     * ```
     *
     * @param {ViewPresetConfig|Object|String|Number} config Config object, preset name or zoom level index.
     * @param {String} [config.preset] Preset name to zoom to. Ignores level config in this case
     * @param {Number} [config.level] Zoom level to zoom to. Is ignored, if preset config is provided
     * @param {VisibleDate} [config.visibleDate] A `visibleDate` specification to bring into view after the zoom.
     * @param {Date} [config.startDate] New time frame start. If provided along with end, view will be centered in this
     * time interval (unless `centerDate` is present)
     * @param {Date} [config.endDate] New time frame end
     * @param {Date} [config.centerDate] Date that should be kept in the center. Has priority over start and end params
     * @param {Date} [config.zoomDate] The date that should be positioned at the passed `datePosition` client offset.
     * @param {Number} [config.zoomPosition] The client offset at which the passed `date` should be positioned.
     * @param {Number} [config.width] Lowest tick width. Might be increased automatically
     * @param {Number} [config.leftMargin] Amount of pixels to extend span start on (used, when zooming to span)
     * @param {Number} [config.rightMargin] Amount of pixels to extend span end on (used, when zooming to span)
     * @param {Number} [config.adjustStart] Amount of units to extend span start on (used, when zooming to span)
     * @param {Number} [config.adjustEnd] Amount of units to extend span end on (used, when zooming to span)
     * @category Zoom
     */
    zoomTo(config) {
      const me = this;
      if (typeof config === "object") {
        if (config.preset) {
          me.zoomToLevel(config.preset, config);
        } else if (config.level != null) {
          me.zoomToLevel(config.level, config);
        } else {
          me.zoomToSpan(config);
        }
      } else {
        me.zoomToLevel(config);
      }
    }
    /**
     * Allows zooming to certain level of {@link Scheduler.view.mixin.TimelineViewPresets#config-presets} array.
     * Automatically limits zooming between {@link #config-maxZoomLevel} and {@link #config-minZoomLevel}. Can also set
     * time axis timespan to the supplied start and end dates.
     *
     * @param {Number} preset Level to zoom to.
     * @param {ChangePresetOptions} [options] Object containing options which affect how the new preset is applied.
     * @returns {Number|null} level Current zoom level or null if it hasn't changed.
     * @category Zoom
     */
    zoomToLevel(preset, options = {}) {
      const me = this;
      if (me.forceFit) {
        console.warn("Warning: The forceFit setting and zooming cannot be combined");
        return null;
      }
      if (typeof preset === "number") {
        preset = Math.min(Math.max(preset, me.minZoomLevel), me.maxZoomLevel);
      }
      const { presets } = me, tickSizeProp = me.isVertical ? "tickHeight" : "tickWidth", newPreset = presets.createRecord(preset), configuredTickSize = newPreset[tickSizeProp], startDate = options.startDate ? new Date(options.startDate) : null, endDate = options.endDate ? new Date(options.endDate) : null;
      presets.add(newPreset);
      let span = startDate && endDate ? { startDate, endDate } : null, scrollableViewportSize = me.isVertical ? me.scrollable.clientHeight : me.timeAxisSubGrid.width;
      const centerDate = options.centerDate ? new Date(options.centerDate) : span ? new Date((startDate.getTime() + endDate.getTime()) / 2) : me.viewportCenterDateCached;
      if (scrollableViewportSize === 0) {
        const { _beforeCollapseState } = me.timeAxisSubGrid;
        if (me.isHorizontal && me.timeAxisSubGrid.collapsed && (_beforeCollapseState == null ? void 0 : _beforeCollapseState.width)) {
          scrollableViewportSize = _beforeCollapseState.width;
        } else {
          return null;
        }
      }
      if (!span) {
        span = me.calculateOptimalDateRange(centerDate, scrollableViewportSize, newPreset);
      }
      if ("width" in options) {
        newPreset.setData(tickSizeProp, options.width);
      }
      me.isZooming = true;
      newPreset.options = {
        ...options,
        startDate: span.startDate || me.startDate,
        endDate: span.endDate || me.endDate,
        centerDate
      };
      me.viewPreset = newPreset;
      newPreset.actualWidth = me.timeAxisViewModel.tickSize;
      me.isZooming = false;
      newPreset.setData(tickSizeProp, configuredTickSize);
      return me.zoomLevel;
    }
    /**
     * Changes the range of the scheduling chart to fit all the events in its event store.
     * @param {Object} [options] Options object for the zooming operation.
     * @param {Number} [options.leftMargin] Defines margin in pixel between the first event start date and first visible
     * date
     * @param {Number} [options.rightMargin] Defines margin in pixel between the last event end date and last visible
     * date
     */
    zoomToFit(options) {
      const eventStore = this.eventStore, span = eventStore.getTotalTimeSpan();
      options = {
        leftMargin: 0,
        rightMargin: 0,
        ...options,
        ...span
      };
      if (options.startDate && options.endDate) {
        if (options.endDate > options.startDate) {
          this.zoomToSpan(options);
        } else {
          this.scrollToDate(options.startDate);
        }
      }
    }
    /**
     * Sets time frame to specified range and applies zoom level which allows to fit all columns to this range.
     *
     * The given time span will be centered in the scheduling view, in the same time, the start/end date of the whole
     * time axis will be extended in the same way as {@link #function-zoomToLevel} method does, to allow scrolling for
     * user.
     *
     * @param {Object} config The time frame.
     * @param {Date} config.startDate The time frame start.
     * @param {Date} config.endDate The time frame end.
     * @param {Date} [config.centerDate] Date that should be kept in the center. Has priority over start and end params
     * @param {Number} [config.leftMargin] Amount of pixels to extend span start on
     * @param {Number} [config.rightMargin] Amount of pixels to extend span end on
     * @param {Number} [config.adjustStart] Amount of units to extend span start on
     * @param {Number} [config.adjustEnd] Amount of units to extend span end on
     *
     * @returns {Number|null} level Current zoom level or null if it hasn't changed.
     * @category Zoom
     */
    zoomToSpan(config = {}) {
      const me = this, { availableSpace } = me.timeAxisViewModel, diffStartEndDates = config.endDate - config.startDate;
      if (availableSpace <= 0) {
        return;
      }
      if (config.leftMargin || config.rightMargin) {
        config.adjustStart = 0;
        config.adjustEnd = 0;
      }
      if (!config.leftMargin)
        config.leftMargin = 0;
      if (!config.rightMargin)
        config.rightMargin = 0;
      if (!config.startDate || !config.endDate)
        throw new Error("zoomToSpan: must provide startDate + endDate dates");
      config.startDate = me.getMinDateOrDate(config.startDate);
      config.endDate = me.getMaxDateOrDate(config.endDate);
      if (config.endDate <= config.startDate) {
        config.endDate = me.getMaxDateOrDate(DateHelper.add(config.startDate, diffStartEndDates));
      }
      const { timeAxis } = me, needToAdjust = config.adjustStart >= 0 || config.adjustEnd >= 0;
      let {
        startDate,
        endDate
      } = config;
      if (needToAdjust) {
        startDate = DateHelper.add(startDate, -config.adjustStart, timeAxis.mainUnit);
        endDate = DateHelper.add(endDate, config.adjustEnd, timeAxis.mainUnit);
      }
      if (startDate <= endDate) {
        const presets = me.presets.allRecords, diffMS = endDate - startDate || 1;
        let currLevel = me.zoomLevel, inc, range;
        if (currLevel === -1) {
          currLevel = 0;
        }
        let msPerPixel = me.getMilliSecondsPerPixelForZoomLevel(presets[currLevel]), candidateLevel = currLevel + (inc = diffMS / msPerPixel + config.leftMargin + config.rightMargin > availableSpace ? -1 : 1), zoomLevel, levelToZoom = null;
        while (candidateLevel >= 0 && candidateLevel <= presets.length - 1) {
          zoomLevel = presets[candidateLevel];
          msPerPixel = me.getMilliSecondsPerPixelForZoomLevel(zoomLevel);
          const spanWidth = diffMS / msPerPixel + config.leftMargin + config.rightMargin;
          if (inc === -1) {
            if (spanWidth <= availableSpace) {
              levelToZoom = candidateLevel;
              break;
            }
          } else {
            if (spanWidth <= availableSpace) {
              if (currLevel !== candidateLevel - inc) {
                levelToZoom = candidateLevel;
              }
            } else {
              break;
            }
          }
          candidateLevel += inc;
        }
        levelToZoom = levelToZoom != null ? levelToZoom : candidateLevel - inc;
        zoomLevel = presets[levelToZoom];
        const unitToZoom = zoomLevel.bottomHeader.unit;
        msPerPixel = me.getMilliSecondsPerPixelForZoomLevel(zoomLevel);
        if (config.leftMargin || config.rightMargin) {
          startDate = new Date(startDate.getTime() - msPerPixel * config.leftMargin);
          endDate = new Date(endDate.getTime() + msPerPixel * config.rightMargin);
        }
        const tickCount = DateHelper.getDurationInUnit(startDate, endDate, unitToZoom, true) / zoomLevel.bottomHeader.increment;
        if (tickCount === 0) {
          return null;
        }
        const customWidth = Math.floor(availableSpace / tickCount), centerDate = config.centerDate || new Date((startDate.getTime() + endDate.getTime()) / 2);
        if (needToAdjust) {
          range = {
            startDate,
            endDate
          };
        } else {
          range = me.calculateOptimalDateRange(centerDate, availableSpace, zoomLevel);
        }
        let result = me.zoomLevel;
        if (me.zoomLevel === levelToZoom) {
          timeAxis.reconfigure(range);
        } else {
          result = me.zoomToLevel(
            levelToZoom,
            Object.assign(range, {
              width: customWidth,
              centerDate
            })
          );
        }
        this.trigger("zoomToSpan", { zoomLevel: levelToZoom, startDate, endDate, centerDate });
        return result;
      }
      return null;
    }
    /**
     * Zooms in the timeline according to the array of zoom levels. If the amount of levels to zoom is given, the view
     * will zoom in by this value. Otherwise, a value of `1` will be used.
     *
     * @param {Number} [levels] (optional) amount of levels to zoom in
     * @param {ChangePresetOptions} [options] Object containing options which affect how the new preset is applied.
     * @returns {Number|null} currentLevel New zoom level of the panel or null if level hasn't changed.
     * @category Zoom
     */
    zoomIn(levels = 1, options) {
      if (typeof levels === "object") {
        options = levels;
        levels = 1;
      }
      const currentZoomLevelIndex = this.zoomLevel;
      if (currentZoomLevelIndex >= this.maxZoomLevel) {
        return null;
      }
      return this.zoomToLevel(currentZoomLevelIndex + levels, options);
    }
    /**
     * Zooms out the timeline according to the array of zoom levels. If the amount of levels to zoom is given, the view
     * will zoom out by this value. Otherwise, a value of `1` will be used.
     *
     * @param {Number} levels (optional) amount of levels to zoom out
     * @param {ChangePresetOptions} [options] Object containing options which affect how the new preset is applied.
     * @returns {Number|null} currentLevel New zoom level of the panel or null if level hasn't changed.
     * @category Zoom
     */
    zoomOut(levels = 1, options) {
      if (typeof levels === "object") {
        options = levels;
        levels = 1;
      }
      const currentZoomLevelIndex = this.zoomLevel;
      if (currentZoomLevelIndex <= this.minZoomLevel) {
        return null;
      }
      return this.zoomToLevel(currentZoomLevelIndex - levels, options);
    }
    /**
     * Zooms in the timeline to the {@link #config-maxZoomLevel} according to the array of zoom levels.
     *
     * @param {ChangePresetOptions} [options] Object containing options which affect how the new preset is applied.
     * @returns {Number|null} currentLevel New zoom level of the panel or null if level hasn't changed.
     * @category Zoom
     */
    zoomInFull(options) {
      return this.zoomToLevel(this.maxZoomLevel, options);
    }
    /**
     * Zooms out the timeline to the {@link #config-minZoomLevel} according to the array of zoom levels.
     *
     * @param {ChangePresetOptions} [options] Object containing options which affect how the new preset is applied.
     * @returns {Number|null} currentLevel New zoom level of the panel or null if level hasn't changed.
     * @category Zoom
     */
    zoomOutFull(options) {
      return this.zoomToLevel(this.minZoomLevel, options);
    }
    /*
     * Adjusts the timespan of the panel to the new zoom level. Used for performance reasons,
     * as rendering too many columns takes noticeable amount of time so their number is limited.
     * @category Zoom
     * @private
     */
    calculateOptimalDateRange(centerDate, viewportSize, viewPreset) {
      const me = this, {
        timeAxis,
        minDate,
        maxDate
      } = me, { bottomHeader } = viewPreset, tickWidth = me.isHorizontal ? viewPreset.tickWidth : viewPreset.tickHeight;
      me._originalConfiguredDateRange = me._originalConfiguredDateRange || { startDate: me.startDate, endDate: me.endDate };
      if (me.zoomKeepsOriginalTimespan) {
        return me.getAllowedDateRange(centerDate, viewPreset);
      }
      const unit = bottomHeader.unit, difference = Math.ceil(viewportSize / tickWidth * bottomHeader.increment * me.visibleZoomFactor / 2);
      let startDate = DateHelper.add(centerDate, -difference, unit), endDate = DateHelper.add(centerDate, difference, unit);
      if (me.isBeforeMinDate(startDate)) {
        startDate = minDate;
        endDate = DateHelper.add(startDate, difference * 2, unit);
      } else if (me.isAfterMaxDate(endDate)) {
        endDate = maxDate;
        startDate = DateHelper.add(endDate, difference * -2, unit);
      }
      return {
        startDate: timeAxis.floorDate(startDate, false, unit, bottomHeader.increment),
        endDate: timeAxis.ceilDate(endDate, false, unit, bottomHeader.increment)
      };
    }
    /*
     * Calculates allowed data range for viewPreset using centerDate, according to maxAllowedTickCount value.
     * If no canterDate, viewPreset passed, current one will be used.
     * @private
     */
    getAllowedDateRange(centerDate, viewPreset) {
      const me = this;
      centerDate = centerDate || new Date((me.startDate.getTime() + me.endDate.getTime()) / 2);
      viewPreset = viewPreset || me.viewPreset;
      const { timeAxis } = me, { bottomHeader } = viewPreset, { maxAllowedTickCount } = timeAxis, incrementDiff = Math.ceil(maxAllowedTickCount * DateHelper.asMilliseconds(bottomHeader.increment, bottomHeader.unit) / 2), maxAllowedEndDate = DateHelper.add(centerDate, incrementDiff);
      if (this.endDate > maxAllowedEndDate) {
        console.warn("Warn: start/end dates exceeds max allowed date range for current viewPreset, it was recalculated automatically.");
        const startDate = DateHelper.add(centerDate, -incrementDiff), endDate = DateHelper.add(centerDate, incrementDiff);
        return { startDate, endDate };
      } else {
        const { startDate, endDate } = me._originalConfiguredDateRange || me;
        if (endDate < maxAllowedEndDate) {
          return { startDate, endDate };
        }
      }
      return {
        startDate: timeAxis.startDate,
        endDate: timeAxis.endDate
      };
    }
    onElementMouseMove(event) {
      const {
        isHorizontal,
        zoomContext
      } = this;
      super.onElementMouseMove(event);
      if (event.isTrusted && zoomContext) {
        if (Math.abs(event[`client${isHorizontal ? "X" : "Y"}`] - zoomContext.coordinate) > 10) {
          this.zoomContext = null;
        }
      }
    }
    onWheel(event) {
      if ((event.ctrlKey || event.metaKey) && !this.forceFit) {
        event.preventDefault();
        const me = this, {
          zoomContext,
          isHorizontal,
          timelineScroller,
          zoomLevel
        } = me, now2 = performance.now(), coordinate = event[`client${isHorizontal ? "X" : "Y"}`];
        let zoomPosition = coordinate - timelineScroller.viewport[`${isHorizontal ? "x" : "y"}`];
        if (isHorizontal && me.rtl) {
          zoomPosition = timelineScroller.viewport.width + timelineScroller.viewport.x - coordinate;
        }
        if (now2 - me.lastWheelTime > 200 || !zoomContext || Math.abs(coordinate - me.zoomContext.coordinate) > 20) {
          me.zoomContext = {
            // So we can track if we're going in (to finer resolutions)
            zoomLevel,
            // Pointer client(X|Y)
            coordinate,
            // Full TimeAxis offset position at which to place the date
            zoomPosition,
            // The date to place at the position
            zoomDate: me.getDateFromDomEvent(event)
          };
        } else {
          if (zoomLevel > zoomContext.zoomLevel) {
            zoomContext.zoomDate = me.getDateFromDomEvent(event);
            zoomContext.zoomLevel = zoomLevel;
          }
          zoomContext.zoomPosition = zoomPosition;
        }
        me.lastWheelTime = now2;
        me[`zoom${event.deltaY > 0 ? "Out" : "In"}`](void 0, me.zoomContext);
      }
    }
    /**
     * Moves the time axis by the passed amount and unit.
     *
     * NOTE: If using a filtered time axis, see {@link Scheduler.data.TimeAxis#function-shift} for more information.
     *
     * @param {Number} amount The number of units to jump
     * @param {DurationUnitShort} [unit] The unit (Day, Week etc.)
     */
    shift(amount, unit) {
      this.timeAxis.shift(amount, unit);
    }
    /**
     * Moves the time axis forward in time in units specified by the view preset `shiftUnit`, and by the amount
     * specified by the `shiftIncrement` config of the current view preset.
     *
     * NOTE: If using a filtered time axis, see {@link Scheduler.data.TimeAxis#function-shiftNext} for more information.
     *
     * @param {Number} [amount] The number of units to jump forward
     */
    shiftNext(amount) {
      this.timeAxis.shiftNext(amount);
    }
    /**
     * Moves the time axis backward in time in units specified by the view preset `shiftUnit`, and by the amount
     * specified by the `shiftIncrement` config of the current view preset.
     *
     * NOTE: If using a filtered time axis, see {@link Scheduler.data.TimeAxis#function-shiftPrevious} for more
     * information.
     *
     * @param {Number} [amount] The number of units to jump backward
     */
    shiftPrevious(amount) {
      this.timeAxis.shiftPrevious(amount);
    }
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "TimelineZoomable"), __publicField(_a5, "defaultConfig", {
    /**
     * If true, you can zoom in and out on the time axis using CTRL-key + mouse wheel.
     * @config {Boolean}
     * @default
     * @category Zoom
     */
    zoomOnMouseWheel: true,
    /**
     * True to zoom to time span when double-clicking a time axis cell.
     * @config {Boolean}
     * @default
     * @category Zoom
     */
    zoomOnTimeAxisDoubleClick: true,
    /**
     * The minimum zoom level to which {@link #function-zoomOut} will work. Defaults to 0 (year ticks)
     * @config {Number}
     * @category Zoom
     * @default
     */
    minZoomLevel: 0,
    /**
     * The maximum zoom level to which {@link #function-zoomIn} will work. Defaults to the number of
     * {@link Scheduler.preset.ViewPreset ViewPresets} available, see {@link Scheduler/view/mixin/TimelineViewPresets#property-presets}
     * for information. Unless you have modified the collection of available presets, the max zoom level is
     * milliseconds.
     * @config {Number}
     * @category Zoom
     * @default 23
     */
    maxZoomLevel: null,
    /**
     * Integer number indicating the size of timespan during zooming. When zooming, the timespan is adjusted to make
     * the scrolling area `visibleZoomFactor` times wider than the timeline area itself. Used in
     * {@link #function-zoomToSpan} and {@link #function-zoomToLevel} functions.
     * @config {Number}
     * @default
     * @category Zoom
     */
    visibleZoomFactor: 5,
    /**
     * Whether the originally rendered timespan should be preserved while zooming. By default, it is set to `false`,
     * meaning the timeline panel will adjust the currently rendered timespan to limit the amount of HTML content to
     * render. When setting this option to `true`, be careful not to allow to zoom a big timespan in seconds
     * resolution for example. That will cause **a lot** of HTML content to be rendered and affect performance. You
     * can use {@link #config-minZoomLevel} and {@link #config-maxZoomLevel} config options for that.
     * @config {Boolean}
     * @default
     * @category Zoom
     */
    zoomKeepsOriginalTimespan: null
  }), _a5;
};

// ../Scheduler/lib/Scheduler/view/recurrence/RecurrenceConfirmationPopup.js
var getAncestorWidget = (w) => {
  var _a5;
  return (_a5 = w.enableRecurringEvents) == null ? void 0 : _a5.defaultAction;
};
var _RecurrenceConfirmationPopup = class _RecurrenceConfirmationPopup extends Popup {
  /**
   * Reference to the "Apply changes to multiple occurrences" button, if used
   * @property {Core.widget.Button}
   * @readonly
   */
  get changeMultipleButton() {
    return this.widgetMap.changeMultipleButton;
  }
  /**
   * Reference to the button that causes changing of the event itself only, if used
   * @property {Core.widget.Button}
   * @readonly
   */
  get changeSingleButton() {
    return this.widgetMap.changeSingleButton;
  }
  /**
   * Reference to the cancel button, if used
   * @property {Core.widget.Button}
   * @readonly
   */
  get cancelButton() {
    return this.widgetMap.cancelButton;
  }
  /**
   * Handler for "Apply changes to multiple occurrences" {@link #property-changeMultipleButton button}.
   * It calls {@link #function-processMultipleRecords} and then hides the dialog.
   */
  onChangeMultipleButtonClick() {
    this.processMultipleRecords();
    this.hide();
  }
  /**
   * Handler for the {@link #property-changeSingleButton button} that causes changing of the event itself only.
   * It calls {@link #function-processSingleRecord} and then hides the dialog.
   */
  onChangeSingleButtonClick() {
    this.processSingleRecord();
    this.hide();
  }
  /**
   * Handler for {@link #property-cancelButton cancel button}.
   * It calls `cancelFn` provided to {@link #function-confirm} call and then hides the dialog.
   */
  onCancelButtonClick() {
    var _a5;
    (_a5 = this.cancelFn) == null ? void 0 : _a5.call(this.thisObj);
    this.hide();
  }
  onRecurrenceClose() {
    var _a5;
    (_a5 = this.cancelFn) == null ? void 0 : _a5.call(this.thisObj);
    this.hide();
  }
  /**
   * Displays the confirmation.
   *
   * This function is async, and awaitable, resolving only when the process has been either completed or canceled
   * Usage example:
   *
   * ```javascript
   * const popup = new RecurrenceConfirmationPopup();
   *
   * await popup.confirm({
   *     eventRecord,
   *     actionType : "delete",
   *     changerFn  : () => eventStore.remove(record)
   * });
   *
   * Toast.show('Event deleted');
   * ```
   *
   * @param {Object} config The following config options are supported:
   * @param {Scheduler.model.EventModel} config.eventRecord   Event being modified.
   * @param {'update'|'delete'} config.actionType Type of modification to be applied to the event. Can be
   * either "update" or "delete".
   * @param {Function} config.changerFn A function that should be called to apply the change to the event upon user
   * choice.
   * @param {Function} [config.thisObj] `changerFn` and `cancelFn` functions scope.
   * @param {Function} [config.cancelFn] Function called on `Cancel` button click.
   * @returns {Boolean} `true` if the operation has been completed either way, `false` if the operation was canceled.
   * @async
   */
  confirm(config = {}) {
    var _a5;
    const me = this, owner = me.closest(getAncestorWidget), defaultAction = (_a5 = owner == null ? void 0 : owner.enableRecurringEvents) == null ? void 0 : _a5.defaultAction;
    [
      "actionType",
      "eventRecord",
      "title",
      "html",
      "changerFn",
      "cancelFn",
      "finalizerFn",
      "thisObj"
    ].forEach((prop) => {
      if (prop in config)
        me[prop] = config[prop];
    });
    if (defaultAction === "single") {
      me.processSingleRecord();
      return true;
    } else if (defaultAction === "future") {
      me.processMultipleRecords();
      return true;
    }
    me.confirmed = false;
    me.updatePopupContent();
    super.show(config);
    return new Promise((resolve) => me.resolve = resolve);
  }
  updatePopupContent() {
    const me = this, { changeMultipleButton, changeSingleButton } = me.widgetMap, { eventRecord, actionType = "update" } = me, isMaster = eventRecord == null ? void 0 : eventRecord.isRecurring;
    if (!changeMultipleButton || !changeSingleButton) {
      (changeMultipleButton || changeSingleButton).text = me.L("L{Object.Yes}");
      if (isMaster) {
        me.html = me.L(`${actionType}-all-message`);
      } else {
        if (changeMultipleButton) {
          me.html = me.L(`${actionType}-all-message`);
        } else {
          me.html = me.L(`${actionType}-only-this-message`);
        }
      }
    } else {
      if (isMaster) {
        if (!changeMultipleButton.initialConfig.text) {
          changeMultipleButton.text = me.L("L{Object.Yes}");
        }
        me.html = me.L(`${actionType}-all-message`);
      } else {
        if (!changeMultipleButton.initialConfig.text) {
          changeMultipleButton.text = me.L(`${actionType}-further-btn-text`);
        }
        me.html = me.L(`${actionType}-further-message`);
      }
      if (!changeSingleButton.initialConfig.text) {
        changeSingleButton.text = me.L(`${actionType}-only-this-btn-text`);
      }
    }
    me.width = me.L("L{width}");
    me.title = me.L(`${actionType}-title`);
  }
  /**
   * Applies changes to multiple occurrences as reaction on "Apply changes to multiple occurrences"
   * {@link #property-changeMultipleButton button} click.
   */
  processMultipleRecords() {
    const { eventRecord, changerFn, thisObj, finalizerFn } = this;
    eventRecord.beginBatch();
    changerFn && this.callback(changerFn, thisObj, [eventRecord]);
    eventRecord.endBatch();
    finalizerFn && this.callback(finalizerFn, thisObj, [eventRecord]);
    this.confirmed = true;
  }
  /**
   * Applies changes to a single record by making it a "real" event and adding an exception to the recurrence.
   * The method is called as reaction on clicking the {@link #property-changeSingleButton button} that causes changing of the event itself only.
   */
  processSingleRecord() {
    const { eventRecord, changerFn, thisObj, finalizerFn } = this;
    eventRecord.beginBatch();
    if (eventRecord.isRecurring) {
      let firstOccurrence;
      eventRecord.recurrence.forEachOccurrence(eventRecord.startDate, null, (occurrence, isFirst, index) => {
        if (index > 1) {
          firstOccurrence = occurrence;
          return false;
        }
      });
      firstOccurrence == null ? void 0 : firstOccurrence.convertToRealEvent();
    }
    eventRecord.recurrence = null;
    changerFn && this.callback(changerFn, thisObj, [eventRecord]);
    eventRecord.recurrenceRule = null;
    eventRecord.endBatch();
    finalizerFn && this.callback(finalizerFn, thisObj, [eventRecord]);
    this.confirmed = true;
  }
  updateLocalization() {
    this.updatePopupContent();
    super.updateLocalization();
  }
  hide() {
    var _a5;
    super.hide();
    (_a5 = this.resolve) == null ? void 0 : _a5.call(this, this.confirmed);
    this.resolve = null;
  }
};
__publicField(_RecurrenceConfirmationPopup, "$name", "RecurrenceConfirmationPopup");
// Factoryable type name
__publicField(_RecurrenceConfirmationPopup, "type", "recurrenceconfirmation");
__publicField(_RecurrenceConfirmationPopup, "configurable", {
  localizableProperties: [],
  align: "b-t",
  autoShow: false,
  autoClose: false,
  closeAction: "onRecurrenceClose",
  modal: true,
  centered: true,
  scrollAction: "realign",
  constrainTo: globalThis,
  draggable: true,
  closable: true,
  floating: true,
  eventRecord: null,
  cls: "b-sch-recurrenceconfirmation",
  bbar: {
    defaults: {
      localeClass: _RecurrenceConfirmationPopup
    },
    items: {
      changeSingleButton: {
        weight: 100,
        cls: "b-raised",
        color: "b-blue",
        onClick: "up.onChangeSingleButtonClick"
      },
      changeMultipleButton: {
        weight: 200,
        color: "b-green",
        onClick: "up.onChangeMultipleButtonClick"
      },
      cancelButton: {
        weight: 300,
        color: "b-gray",
        text: "L{Object.Cancel}",
        onClick: "up.onCancelButtonClick"
      }
    }
  }
});
var RecurrenceConfirmationPopup = _RecurrenceConfirmationPopup;
RecurrenceConfirmationPopup.initClass();
RecurrenceConfirmationPopup._$name = "RecurrenceConfirmationPopup";

// ../Scheduler/lib/Scheduler/view/mixin/RecurringEvents.js
var RecurringEvents_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    construct(config) {
      super.construct(config);
      this.ion({
        beforeEventDropFinalize: "onRecurrableBeforeEventDropFinalize",
        beforeEventResizeFinalize: "onRecurrableBeforeEventResizeFinalize",
        beforeAssignmentDelete: "onRecurrableAssignmentBeforeDelete"
      });
    }
    changeRecurrenceConfirmationPopup(recurrenceConfirmationPopup, oldRecurrenceConfirmationPopup) {
      if (recurrenceConfirmationPopup) {
        return RecurrenceConfirmationPopup.reconfigure(oldRecurrenceConfirmationPopup, RecurrenceConfirmationPopup.mergeConfigs({
          owner: this
        }, recurrenceConfirmationPopup), this);
      }
    }
    findRecurringEventToConfirmDelete(eventRecords) {
      return eventRecords.find((eventRecord) => eventRecord.supportsRecurring && (eventRecord.isRecurring || eventRecord.isOccurrence));
    }
    onRecurrableAssignmentBeforeDelete({ assignmentRecords, context }) {
      const eventRecords = assignmentRecords.map((as) => as.event), eventRecord = this.findRecurringEventToConfirmDelete(eventRecords);
      if (this.enableRecurringEvents && eventRecord) {
        this.recurrenceConfirmationPopup.confirm({
          actionType: "delete",
          eventRecord,
          changerFn() {
            context.finalize(true);
          },
          cancelFn() {
            context.finalize(false);
          }
        });
        return false;
      }
    }
    onRecurrableBeforeEventDropFinalize({ context }) {
      if (this.enableRecurringEvents) {
        const { eventRecords } = context, recurringEvents = eventRecords.filter((eventRecord) => eventRecord.supportsRecurring && (eventRecord.isRecurring || eventRecord.isOccurrence));
        if (recurringEvents.length) {
          context.async = true;
          this.recurrenceConfirmationPopup.confirm({
            actionType: "update",
            eventRecord: recurringEvents[0],
            changerFn() {
              context.finalize(true);
            },
            cancelFn() {
              context.finalize(false);
            }
          });
        }
      }
    }
    onRecurrableBeforeEventResizeFinalize({ context }) {
      if (this.enableRecurringEvents) {
        const { eventRecord } = context, isRecurring = eventRecord.supportsRecurring && (eventRecord.isRecurring || eventRecord.isOccurrence);
        if (isRecurring) {
          context.async = true;
          this.recurrenceConfirmationPopup.confirm({
            actionType: "update",
            eventRecord,
            changerFn() {
              context.finalize(true);
            },
            cancelFn() {
              context.finalize(false);
            }
          });
        }
      }
    }
    // Make sure occurrence cache is up-to-date when reassigning events
    onAssignmentChange({ action, records: assignments, changes }) {
      var _a6;
      if (action === "update" && changes.event && changes.resource && Object.keys(changes).length === 2) {
        return;
      }
      if (action !== "dataset" && Array.isArray(assignments)) {
        for (const assignment of assignments) {
          if (((_a6 = assignment.event) == null ? void 0 : _a6.isRecurring) && !assignment.event.isBatchUpdating) {
            assignment.event.removeOccurrences(this.eventStore);
          }
        }
      }
    }
    /**
     * Returns occurrences of the provided recurring event across the date range of this Scheduler.
     * @param  {Scheduler.model.TimeSpan} recurringEvent Recurring event for which occurrences should be retrieved.
     * @returns {Scheduler.model.TimeSpan[]} Array of the provided timespans occurrences.
     *
     * __Empty if the passed event is not recurring, or has no occurrences in the date range.__
     *
     * __If the date range encompasses the start point, the recurring event itself will be the first entry.__
     * @category Data
     */
    getOccurrencesFor(recurringEvent) {
      return this.eventStore.getOccurrencesForTimeSpan(recurringEvent, this.timeAxis.startDate, this.timeAxis.endDate);
    }
    /**
     * Internal utility function to remove events. Used when pressing [DELETE] or [BACKSPACE] or when clicking the
     * delete button in the event editor. Triggers a preventable `beforeEventDelete` or `beforeAssignmentDelete` event.
     *
     * This function is asynchronous because it potentially has to show a {@link Scheduler.view.recurrence.RecurrenceConfirmationPopup}
     * which prompts the user to confirm the operation.
     *
     * In these cases, the Promise is resolved when the user clicks the desired outcome.
     * @param {Scheduler.model.EventModel[]|Scheduler.model.AssignmentModel[]} eventRecords Records to remove
     * @param {Function} [callback] Optional callback executed after triggering the event but before deletion
     * @returns {Boolean} Returns `false` if the operation was prevented, otherwise `true`
     * @internal
     * @fires beforeEventDelete
     * @fires beforeAssignmentDelete
     * @async
     */
    async removeEvents(eventRecords, callback = null, popupOwner = this) {
      const me = this;
      if (!me.readOnly && eventRecords.length) {
        const context = {
          finalize(removeRecord = true) {
            if (callback) {
              callback(removeRecord);
            }
            if (removeRecord !== false) {
              if (eventRecords.some((record) => {
                var _a6;
                return record.isOccurrence || ((_a6 = record.event) == null ? void 0 : _a6.isOccurrence);
              })) {
                eventRecords.forEach((record) => record.isOccurrenceAssignment ? record.event.remove() : record.remove());
              } else {
                const store = eventRecords[0].isAssignment ? me.assignmentStore : me.eventStore;
                store.remove(eventRecords);
              }
            }
          }
        };
        let shouldFinalize;
        if (eventRecords[0].isAssignment) {
          shouldFinalize = await me.trigger("beforeAssignmentDelete", { assignmentRecords: eventRecords, context });
        } else {
          shouldFinalize = await me.trigger("beforeEventDelete", { eventRecords, context });
        }
        if (shouldFinalize !== false) {
          const recurringEventRecord = eventRecords.find((eventRecord) => eventRecord.isRecurring || eventRecord.isOccurrence);
          if (recurringEventRecord) {
            me.recurrenceConfirmationPopup.owner = popupOwner;
            return await me.recurrenceConfirmationPopup.confirm({
              actionType: "delete",
              eventRecord: recurringEventRecord,
              changerFn() {
                context.finalize(true);
              },
              cancelFn() {
                context.finalize(false);
              }
            });
          } else {
            context.finalize(true);
          }
          return true;
        }
      }
      return false;
    }
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "RecurringEvents"), __publicField(_a5, "configurable", {
    /**
     * Enables showing occurrences of recurring events across the scheduler's time axis. If you want to disable
     * the recurrence popup, you can choose set the `defaultAction` to `future` to affect all future
     * occurrences, or `single` to just affect the currently selected event.
     *
     * Enables extra recurrence UI fields in the system-provided event editor (not in Scheduler Pro's task editor).
     * @config {Boolean|Object}
     * @property {'single'|'future'} defaultAction
     * @default
     * @category Scheduled events
     */
    enableRecurringEvents: false,
    /**
     * The confirmation dialog shown when a recurring event is edited.
     *
     * The user is given the opportunity to either change just the occurrence being edited, or
     * all occurrences from that point onwards.
     *
     * The {Scheduler.view.recurrence.RecurrenceConfirmationPopup} may be reconfigured from its default
     * configuration by specifying a config object here.
     * @prp {Scheduler.view.recurrence.RecurrenceConfirmationPopup}
     * @accepts {RecurrenceConfirmationPopupConfig}
     */
    recurrenceConfirmationPopup: {
      $config: ["lazy"],
      value: {
        type: "recurrenceconfirmation"
      }
    }
  }), _a5;
};

// ../Scheduler/lib/Scheduler/view/mixin/TimelineEventRendering.js
var TimelineEventRendering_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    //region Default config
    static get defaultConfig() {
      return {
        /**
         * When `true`, events are sized and positioned based on rowHeight, resourceMargin and barMargin settings.
         * Set this to `false` if you want to control height and vertical position using CSS instead.
         *
         * Note that events always get an absolute top position, but when this setting is enabled that position
         * will match row's top. To offset within the row using CSS, use `transform : translateY(y)`.
         *
         * @config {Boolean}
         * @default
         * @category Scheduled events
         */
        managedEventSizing: true,
        /**
         * The CSS class added to an event/assignment when it is newly created
         * in the UI and unsynced with the server.
         * @config {String}
         * @default
         * @private
         * @category CSS
         */
        generatedIdCls: "b-sch-dirty-new",
        /**
         * The CSS class added to an event when it has unsaved modifications
         * @config {String}
         * @default
         * @private
         * @category CSS
         */
        dirtyCls: "b-sch-dirty",
        /**
         * The CSS class added to an event when it is currently committing changes
         * @config {String}
         * @default
         * @private
         * @category CSS
         */
        committingCls: "b-sch-committing",
        /**
         * The CSS class added to an event/assignment when it ends outside of the visible time range.
         * @config {String}
         * @default
         * @private
         * @category CSS
         */
        endsOutsideViewCls: "b-sch-event-endsoutside",
        /**
         * The CSS class added to an event/assignment when it starts outside of the visible time range.
         * @config {String}
         * @default
         * @private
         * @category CSS
         */
        startsOutsideViewCls: "b-sch-event-startsoutside",
        /**
         * The CSS class added to an event/assignment when it is not draggable.
         * @config {String}
         * @default
         * @private
         * @category CSS
         */
        fixedEventCls: "b-sch-event-fixed"
      };
    }
    //endregion
    //region Settings
    updateFillTicks(fillTicks) {
      const me = this;
      if (!me.isConfiguring) {
        const { timeAxis } = me;
        timeAxis.forceFullTicks = fillTicks && me.snap;
        if (fillTicks) {
          me.timeResolution = {
            unit: timeAxis.mainUnit,
            increment: 1
          };
        }
        me.refreshWithTransition();
        me.trigger("stateChange");
      }
    }
    changeBarMargin(margin) {
      ObjectHelper.assertNumber(margin, "barMargin", true);
      if (this._resourceMargin == null) {
        this.changeResourceMargin(margin);
      }
      if (this.isHorizontal && this.rowHeight) {
        return Math.min(Math.ceil(this.rowHeight / 2), margin);
      }
      return margin;
    }
    updateBarMargin() {
      if (this.rendered) {
        this.currentOrientation.onBeforeRowHeightChange();
        this.refreshWithTransition();
        this.trigger("stateChange");
      }
    }
    // Documented in SchedulerEventRendering to not show up in Gantt docs
    get resourceMargin() {
      return this._resourceMargin == null ? this.barMargin : this._resourceMargin;
    }
    changeResourceMargin(margin) {
      const me = this;
      if (margin == null) {
        margin = this.resourceMargin;
      }
      ;
      if (typeof margin === "number") {
        if (me.isHorizontal && me.rowHeight) {
          margin = Math.min(Math.floor(me.rowHeight / 2), margin);
        }
        me.resourceMarginObject = {
          start: margin,
          end: margin,
          total: margin * 2
        };
        return margin;
      }
      if (!(margin == null ? void 0 : margin.start)) {
        margin.start = 0;
      }
      if (!(margin == null ? void 0 : margin.end)) {
        margin.end = 0;
      }
      if (me.isHorizontal && me.rowHeight) {
        margin.start = me.rowHeight < margin.start + margin.end ? Math.ceil(me.rowHeight / 2) : margin.start;
        margin.end = me.rowHeight < margin.start + margin.end ? Math.ceil(me.rowHeight / 2) : margin.end;
      }
      me.resourceMarginObject = ObjectHelper.assign({
        total: margin.start + margin.end
      }, margin);
      return margin;
    }
    updateResourceMargin() {
      const me = this;
      if (me.rendered) {
        me.currentOrientation.onBeforeRowHeightChange();
        me.refreshWithTransition();
      }
    }
    changeTickSize(width) {
      ObjectHelper.assertNumber(width, "tickSize", true);
      return width;
    }
    updateTickSize(width) {
      const me = this, { timeAxisViewModel } = me;
      let timelineScroller = null, scrollAdjustment = 0;
      if (!me.isConfiguring && me.isHorizontal) {
        timelineScroller = me.timelineScroller;
        const pxScrollToCenter = timelineScroller.viewport.width / 2 + timelineScroller.scrollLeft * (me.rtl ? -1 : 1), centerDate = me.getDateFromCoordinate(pxScrollToCenter);
        if (centerDate) {
          const centerTick = me.timeAxis.getTickFromDate(centerDate);
          scrollAdjustment = centerTick * (width - timeAxisViewModel.tickSize);
        }
      }
      timeAxisViewModel.tickSize = width;
      if (scrollAdjustment) {
        timelineScroller.scrollBy(scrollAdjustment, 0);
      }
      me.trigger("tickSizeChange", { tickSize: width });
    }
    get tickSize() {
      return this.timeAxisViewModel.tickSize;
    }
    /**
     * Predefined event colors, useful in combos etc.
     * @type {String[]}
     * @category Scheduled events
     */
    static get eventColors() {
      return ["red", "pink", "magenta", "purple", "violet", "deep-purple", "indigo", "blue", "light-blue", "cyan", "teal", "green", "light-green", "gantt-green", "lime", "yellow", "orange", "amber", "deep-orange", "light-gray", "gray", "black"];
    }
    /**
     * Predefined event styles , useful in combos etc.
     * @type {String[]}
     * @category Scheduled events
     */
    static get eventStyles() {
      return ["plain", "border", "hollow", "colored", "line", "dashed", "minimal", "rounded"];
    }
    updateEventStyle(style) {
      if (!this.isConfiguring) {
        this.refreshWithTransition();
        this.trigger("stateChange");
      }
    }
    updateEventColor(color) {
      if (!this.isConfiguring) {
        this.refreshWithTransition();
        this.trigger("stateChange");
      }
    }
    //endregion
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "TimelineEventRendering"), __publicField(_a5, "configurable", {
    /**
     * Controls how much space to leave between stacked event bars in px.
     *
     * Value will be constrained by half the row height in horizontal mode.
     *
     * @prp {Number}
     * @default
     * @category Scheduled events
     */
    barMargin: 10,
    /**
     * Specify `true` to force rendered events/tasks to fill entire ticks. This only affects rendering, start
     * and end dates retain their value on the data level.
     *
     * When enabling `fillTicks`, drag-drop interactions use the same granularity as the bottom time
     * axis "ticks". This means if you are viewing a day schedule, and you move an event starting 8am from Monday to
     * Tuesday, it will still always start at 8am. Enabling {@link Scheduler/view/mixin/TimelineDateMapper#config-snap}
     * might also improve the UX.
     *
     * @prp {Boolean}
     * @default
     * @category Scheduled events
     */
    fillTicks: false,
    resourceMargin: null,
    /**
     * Event color used by default. Events and resources can specify their own color, with priority order being:
     * Event -> Resource -> Scheduler default.
     *
     * Specify `null` to not apply a default color and take control using custom CSS (an easily overridden color
     * will be used to make sure events are still visible).
     *
     * For available standard colors, see {@link Scheduler.model.mixin.EventModelMixin#typedef-EventColor}.
     *
     * @prp {EventColor} eventColor
     * @category Scheduled events
     */
    eventColor: "green",
    /**
     * Event style used by default. Events and resources can specify their own style, with priority order being:
     * Event -> Resource -> Scheduler default. Determines the appearance of the event by assigning a CSS class
     * to it. Available styles are:
     *
     * * `'plain'` (default) - flat look
     * * `'border'` - has border in darker shade of events color
     * * `'colored'` - has colored text and wide left border in same color
     * * `'hollow'` - only border + text until hovered
     * * `'line'` - as a line with the text below it
     * * `'dashed'` - as a dashed line with the text below it
     * * `'minimal'` - as a thin line with small text above it
     * * `'rounded'` - minimalistic style with rounded corners
     * * `null` - do not apply a default style and take control using custom CSS (easily overridden basic styling will be used).
     *
     * In addition, there are two styles intended to be used when integrating with Bryntum Calendar. To match
     * the look of Calendar events, you can use:
     *
     * * `'calendar'` - a variation of the "colored" style matching the default style used by Calendar
     * * `'interday'` - a variation of the "plain" style, for interday events
     *
     * @prp {'plain'|'border'|'colored'|'hollow'|'line'|'dashed'|'minimal'|'rounded'|'calendar'|'interday'|null}
     * @default
     * @category Scheduled events
     */
    eventStyle: "plain",
    /**
     * The width/height (depending on vertical / horizontal mode) of all the time columns.
     *
     * There is a limit for the tick size value. Its minimal allowed value is calculated so ticks would fit the
     * available space. Only applicable when {@link Scheduler.view.TimelineBase#config-forceFit} is set to
     * `false`. To set `tickSize` freely skipping that limitation please set
     * {@link Scheduler.view.TimelineBase#config-suppressFit} to `true`.
     *
     * @prp {Number}
     * @category Scheduled events
     */
    tickSize: null
  }), _a5;
};

// ../Scheduler/lib/Scheduler/view/mixin/TimelineEarlyPositioning.js
var MIN_DATE = /* @__PURE__ */ new Date(-864e13);
var MAX_DATE = /* @__PURE__ */ new Date(864e13);
var TimelineEarlyPositioning_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    //region Row store
    applyViewStartEndDates(startDate, endDate) {
      const me = this, { visibleDate = {} } = me, requestedVisibleDate = visibleDate == null ? void 0 : visibleDate.date, min4 = requestedVisibleDate ? DateHelper.min(startDate, requestedVisibleDate) : startDate != null ? startDate : /* @__PURE__ */ new Date(), max3 = requestedVisibleDate ? endDate ? DateHelper.max(endDate, requestedVisibleDate) : DateHelper.add(min4, me.visibleDateRange.endDate - me.visibleDateRange.startDate) : endDate;
      if (min4 && max3) {
        me.setTimeSpan(min4, max3, { ...visibleDate, visibleDate: requestedVisibleDate });
        return true;
      } else if (min4) {
        me.startDate = min4;
        return true;
      }
      return false;
    }
    onEventStoreDataRefresh(event) {
      if (this._initialStartDateFilled === "pending") {
        this.tryToApplyPreCommitStartEndDates(event.source);
      }
    }
    tryToApplyPreCommitStartEndDates(eventStore) {
      var _a6, _b2;
      const me = this, project = me.project, projectStartDate = (
        // the start date was set as part of the model config
        (_a6 = project.get("startDate")) != null ? _a6 : (
          // or, it was assigned from the crudmanager response,
          // in the latter case - it goes to engine first, bypassing the model's storage
          // so we need to use accessor
          !project.replica ? project.startDate : null
        )
      );
      if (projectStartDate) {
        me.applyViewStartEndDates(
          projectStartDate,
          (_b2 = project.get("endDate")) != null ? _b2 : !project.replica ? project.endDate : null
        );
        me._initialStartDateFilled = "applied";
      } else if (eventStore.count > 0) {
        let min4 = MAX_DATE, max3 = MIN_DATE;
        eventStore.forEach((event) => {
          const startDate = event.get("startDate"), endDate = event.get("endDate");
          if (startDate && startDate < min4) {
            min4 = startDate;
          }
          if (endDate && endDate > max3) {
            max3 = endDate;
          }
        });
        me.applyViewStartEndDates(
          min4.getTime() !== MAX_DATE.getTime() ? min4 : null,
          max3.getTime() !== MIN_DATE.getTime() ? max3 : null
        );
        me._initialStartDateFilled = "applied";
      }
    }
    bindEventStoreForEarlyPositioning(store) {
      var _a6;
      const me = this, initialConfig = me.initialConfig;
      if (me._initialStartDateFilled === void 0 && !initialConfig.startDate && // `date` config is used by calendar
      !initialConfig.date && !((_a6 = initialConfig.timeAxis) == null ? void 0 : _a6.startDate) && !initialConfig.visibleDate && // if this timeline was given a partner, it should not "disturb" the visible daterange -
      // the partner will decide on it
      !initialConfig.partner) {
        me._initialStartDateFilled = "pending";
        me.tryToApplyPreCommitStartEndDates(store);
      }
      me.detachListeners("eventStoreDataRefresh");
      store.ion({
        name: "eventStoreDataRefresh",
        // we also have `onEventStoreRefresh` in some other places - picked the different name
        // the `PreCommit` suffix is needed because PartOfProjectStore buffers all events
        // when engine is not ready, except the ones with suffix
        refreshPreCommit: "onEventStoreDataRefresh",
        // Making sure that we should only react to the first refreshPreCommit event
        once: true,
        thisObj: me
      });
    }
    //endregion
    //region WidgetClass
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
    //endregion
  }, __publicField(_a5, "$name", "TimelineEarlyPositioning"), _a5;
};

// ../Scheduler/lib/Scheduler/view/mixin/TimelineScroll.js
var maintainVisibleStart = {
  maintainVisibleStart: true
};
var defaultScrollOptions4 = {
  block: "nearest"
};
var TimelineScroll_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base).mixin(
    // the mixin overrides TimelineZoomable method so should be applied after it
    TimelineZoomable_default
  ) {
    //region TimelineZoomable injections
    onZoomToSpan({ startDate }) {
      if (this.infiniteScroll) {
        this.scrollToDate(startDate, { block: "start" });
      }
    }
    calculateOptimalDateRange(centerDate, viewportSize, viewPreset) {
      if (this.infiniteScroll) {
        if (this.zoomKeepsOriginalTimespan) {
          const { startDate, endDate } = this.timeAxis;
          return { startDate, endDate };
        }
        return this.calculateInfiniteScrollingDateRange(centerDate, true);
      }
      return super.calculateOptimalDateRange(...arguments);
    }
    //endregion TimelineZoomable injections
    initScroll() {
      var _a6;
      const me = this, {
        isHorizontal,
        visibleDate
      } = me;
      super.initScroll();
      const { scrollable } = isHorizontal ? me.timeAxisSubGrid : me;
      scrollable.ion({
        scroll: "onTimelineScroll",
        thisObj: me
      });
      if (me.infiniteScroll) {
        if ((_a6 = me.partneredWith) == null ? void 0 : _a6.values.some((partner) => partner.infiniteScroll)) {
          const availableSpace = scrollable.element.getBoundingClientRect()[me.isHorizontal ? "width" : "height"];
          me.timeAxisViewModel.update(availableSpace, false, true);
        } else {
          const setTimeSpanOptions2 = visibleDate ? { ...visibleDate, visibleDate: visibleDate.date } : { visibleDate: me.viewportCenterDate, block: "center" }, { startDate, endDate } = me.calculateInfiniteScrollingDateRange(setTimeSpanOptions2.visibleDate, setTimeSpanOptions2.block === "center");
          me.setTimeSpan(
            startDate,
            endDate,
            setTimeSpanOptions2
          );
        }
      }
    }
    /**
     * A {@link Core.helper.util.Scroller} which scrolls the time axis in whatever {@link Scheduler.view.Scheduler#config-mode} the
     * Scheduler is configured, either `horizontal` or `vertical`.
     *
     * The width and height dimensions are replaced by `size`. So this will expose the following properties:
     *
     *    - `clientSize` The size of the time axis viewport.
     *    - `scrollSize` The full scroll size of the time axis viewport
     *    - `position` The position scrolled to along the time axis viewport
     *
     * @property {Core.helper.util.Scroller}
     * @readonly
     * @category Scrolling
     */
    get timelineScroller() {
      const me = this;
      if (!me.scrollInitialized) {
        me.initScroll();
      }
      return me._timelineScroller || (me._timelineScroller = new TimelineScroller({
        widget: me,
        scrollable: me.isHorizontal ? me.timeAxisSubGrid.scrollable : me.scrollable,
        isHorizontal: me.isHorizontal
      }));
    }
    /**
     * Used to calculate the range to extend the TimeAxis to during infinite scroll.
     * @param {Date} date
     * @param {Boolean} centered
     * @param {Scheduler.preset.ViewPreset} [preset] Optional, the preset for which to calculate the range.
     * defaults to the currently active ViewPreset
     * @returns {Object} `{ startDate, endDate }`
     * @internal
     */
    calculateInfiniteScrollingDateRange(date2, centered, preset = this.timeAxisViewModel.viewPreset) {
      const me = this, {
        timeAxisViewModel,
        bufferCoef
      } = me, {
        timeAxis,
        availableSpace
      } = timeAxisViewModel, factor = Math.ceil(timeAxis.totalCount / timeAxis.count), {
        leafUnit,
        leafIncrement,
        topUnit,
        topIncrement,
        tickSize
      } = preset, useTop = leafUnit === topUnit && Math.round(topIncrement) === topIncrement && Math.round(leafIncrement) === leafIncrement, snapSize = useTop ? topIncrement : leafIncrement, snapUnit = useTop ? topUnit : leafUnit;
      if (centered) {
        const halfSpan = Math.ceil((availableSpace * bufferCoef + availableSpace / 2) / tickSize) * factor, startDate = timeAxis.floorDate(DateHelper.add(date2, -halfSpan * leafIncrement, leafUnit), false, snapUnit, snapSize), endDate = timeAxis.ceilDate(DateHelper.add(date2, halfSpan * leafIncrement, leafUnit), false, snapUnit, snapSize), diffStartEndDates = endDate - startDate, startDateAdjMinDate = me.getMinDateOrDate(startDate);
        let endDateAdjMaxDate = me.getMaxDateOrDate(endDate);
        if (endDateAdjMaxDate <= startDateAdjMinDate) {
          endDateAdjMaxDate = me.getMaxDateOrDate(DateHelper.add(startDateAdjMinDate, diffStartEndDates));
        }
        return {
          startDate: startDateAdjMinDate,
          endDate: endDateAdjMaxDate
        };
      } else {
        const bufferedTicks = Math.ceil(availableSpace * bufferCoef / tickSize) * factor, startDate = DateHelper.add(date2, -bufferedTicks * leafIncrement, leafUnit), endDate = DateHelper.add(date2, Math.ceil((availableSpace / tickSize + bufferedTicks) * leafIncrement), leafUnit), diffStartEndDates = endDate - startDate, startDateAdjMinDate = me.getMinDateOrDate(startDate);
        let endDateAdjMaxDate = me.getMaxDateOrDate(endDate);
        if (endDateAdjMaxDate <= startDateAdjMinDate) {
          endDateAdjMaxDate = me.getMaxDateOrDate(DateHelper.add(startDateAdjMinDate, diffStartEndDates));
        }
        return {
          startDate: timeAxis.floorDate(startDateAdjMinDate, false, snapUnit, snapSize),
          endDate: timeAxis.ceilDate(endDateAdjMaxDate, false, snapUnit, snapSize)
        };
      }
    }
    doDestroy() {
      var _a6;
      (_a6 = this._timelineScroller) == null ? void 0 : _a6.destroy();
      super.doDestroy();
    }
    onTimelineScroll() {
      if (this.infiniteScroll) {
        this.checkTimeAxisScroll();
      }
    }
    checkTimeAxisScroll() {
      const me = this, {
        minDate,
        maxDate
      } = me, {
        startDate,
        endDate
      } = me.timeAxis, scrollable = me.timelineScroller, {
        position,
        clientSize
      } = scrollable, requiredSize = clientSize * me.bufferCoef, limit = requiredSize * me.bufferThreshold, maxScroll = scrollable.maxPosition, { style } = me.timeAxisSubGrid.virtualScrollerElement, closeToStart = position < limit && (!minDate || DateHelper.isAfter(startDate, minDate)), closeToEnd = maxScroll - position < limit && (!maxDate || DateHelper.isBefore(endDate, maxDate));
      if (closeToStart || closeToEnd) {
        style.overflow = "hidden";
        style.pointerEvents = "none";
        style.paddingBottom = `${DomHelper.scrollBarWidth}px`;
        me.setTimeout(() => {
          style.overflow = "";
          style.paddingBottom = "";
          style.pointerEvents = "";
        }, 100);
        let datePos;
        if (closeToStart && closeToEnd) {
          datePos = scrollable.position + clientSize / 2;
        } else {
          datePos = scrollable.position;
        }
        me.shiftToDate(me.getDateFromCoordinate(datePos, "floor", true, false, true), closeToStart && closeToEnd);
      }
    }
    shiftToDate(date2, centered) {
      const newRange = this.calculateInfiniteScrollingDateRange(date2, centered);
      this.setTimeSpan(newRange.startDate, newRange.endDate, centered ? centered === true ? {
        visibleDate: this.viewportCenterDate,
        block: "center"
      } : centered : maintainVisibleStart);
    }
    // If we change to infinite scrolling dynamically, it should create the buffer zones.
    updateInfiniteScroll(infiniteScroll) {
      if (!this.isConfiguring && infiniteScroll) {
        this.checkTimeAxisScroll();
      }
    }
    //region Scroll to date
    /**
     * Scrolls the timeline "tick" encapsulating the passed `Date` into view according to the passed options.
     * @param {Date} date The date to which to scroll the timeline
     * @param {BryntumScrollOptions} [options] How to scroll.
     * @returns {Promise} A Promise which resolves when the scrolling is complete.
     * @category Scrolling
     */
    async scrollToDate(date2, options = {}) {
      const me = this;
      if (me.timeAxisSubGrid.width === 0) {
        return;
      }
      const {
        timeAxis,
        visibleDateRange,
        infiniteScroll,
        minDate,
        maxDate
      } = me, {
        unit,
        increment
      } = timeAxis, edgeOffset = options.edgeOffset || 0, visibleWidth = DateHelper.ceil(visibleDateRange.endDate, increment + " " + unit) - DateHelper.floor(visibleDateRange.startDate, increment + " " + unit), direction = date2 > me.viewportCenterDate ? 1 : -1, extraScroll = (infiniteScroll ? visibleWidth * me.bufferCoef * me.bufferThreshold : options.block === "center" ? visibleWidth / 2 : edgeOffset ? me.getMilliSecondsPerPixelForZoomLevel(me.viewPreset) * edgeOffset : 0) * direction, visibleDate = new Date(date2.getTime() + extraScroll), shiftDirection = visibleDate > timeAxis.endDate ? 1 : visibleDate < timeAxis.startDate ? -1 : 0;
      if (me.isBeforeMinDate(date2) || me.isAfterMaxDate(date2)) {
        date2 = minDate && date2 < minDate ? minDate : maxDate;
        return me.scrollToDate(date2, options);
      }
      if (shiftDirection && me.infiniteScroll) {
        await me.nextAnimationFrame();
        me.shiftToDate(new Date(date2 - extraScroll), null, true);
        await me.nextAnimationFrame();
      }
      const scrollerViewport = me.timelineScroller.viewport, localCoordinate = me.getCoordinateFromDate(date2, true), width = Math.min(me.timeAxisViewModel.tickSize, me.timeAxisViewModel.availableSpace), target = me.isHorizontal ? new Rectangle(me.getCoordinateFromDate(date2, false) - (me.rtl ? width : 0), scrollerViewport.y, width, scrollerViewport.height) : new Rectangle(scrollerViewport.x, me.getCoordinateFromDate(date2, false), scrollerViewport.width, me.timeAxisViewModel.tickSize);
      await me.scrollToCoordinate(localCoordinate, target, date2, options);
    }
    /**
     * Scrolls to current time.
     * @param {BryntumScrollOptions} [options] How to scroll.
     * @returns {Promise} A Promise which resolves when the scrolling is complete.
     * @category Scrolling
     */
    scrollToNow(options = {}) {
      return this.scrollToDate(/* @__PURE__ */ new Date(), options);
    }
    /**
     * Used by {@link #function-scrollToDate} to scroll to correct coordinate.
     * @param {Number} localCoordinate Coordinate to scroll to
     * @param {Element|Core.helper.util.Rectangle} target The target
     * @param {Date} date Date to scroll to, used for reconfiguring the time axis
     * @param {BryntumScrollOptions} [options] How to scroll.
     * @returns {Promise} A Promise which resolves when the scrolling is complete.
     * @private
     * @category Scrolling
     */
    async scrollToCoordinate(localCoordinate, target, date2, options = {}) {
      const me = this, {
        startDate,
        endDate,
        hasTimelineBoundaries,
        minDate,
        maxDate
      } = me, {
        isContinuous,
        unit,
        increment
      } = me.timeAxis;
      if (localCoordinate < 0 || isContinuous && (date2 < startDate || date2 > endDate)) {
        const visibleSpan = endDate - startDate;
        let newStartDate = DateHelper.floor(new Date(date2.getTime() - visibleSpan / 2), increment + " " + unit), newEndDate = DateHelper.add(newStartDate, visibleSpan);
        if (hasTimelineBoundaries) {
          if (!me.isBeforeMinDate(date2) && !me.isAfterMaxDate(date2)) {
            if (me.isBeforeMinDate(newStartDate)) {
              newStartDate = minDate;
              newEndDate = DateHelper.add(newStartDate, visibleSpan);
            } else if (me.isAfterMaxDate(newEndDate)) {
              newEndDate = maxDate;
              newStartDate = DateHelper.add(newEndDate, -visibleSpan);
            }
          } else {
            date2 = minDate && date2 < minDate ? minDate : maxDate;
            return me.scrollToDate(date2, options);
          }
        }
        if (newStartDate - startDate !== 0 && newEndDate - endDate !== 0) {
          await me.setTimeSpan(newStartDate, newEndDate);
          return me.scrollToDate(date2, options);
        }
        return;
      }
      await me.timelineScroller.scrollIntoView(target, options);
      return !me.isDestroyed && me.nextAnimationFrame();
    }
    //endregion
    //region Relative scrolling
    // These methods are important to users because although they are mixed into the top level Grid/Scheduler,
    // for X scrolling the explicitly target the SubGrid that holds the scheduler.
    /**
     * Get/set the `scrollLeft` value of the SubGrid that holds the scheduler.
     *
     * This may be __negative__ when the writing direction is right-to-left.
     * @property {Number}
     * @category Scrolling
     */
    set scrollLeft(left) {
      this.timeAxisSubGrid.scrollable.element.scrollLeft = left;
    }
    get scrollLeft() {
      return this.timeAxisSubGrid.scrollable.element.scrollLeft;
    }
    /**
     * Get/set the writing direction agnostic horizontal scroll position.
     *
     * This is always the __positive__ offset from the scroll origin whatever the writing
     * direction in use.
     *
     * Applies to the SubGrid that holds the scheduler
     * @property {Number}
     * @category Scrolling
     */
    set scrollX(x) {
      this.timeAxisSubGrid.scrollable.x = x;
    }
    get scrollX() {
      return this.timeAxisSubGrid.scrollable.x;
    }
    /**
     * Get/set vertical scroll
     * @property {Number}
     * @category Scrolling
     */
    set scrollTop(top) {
      this.scrollable.y = top;
    }
    get scrollTop() {
      return this.scrollable.y;
    }
    /**
     * Horizontal scrolling. Applies to the SubGrid that holds the scheduler
     * @param {Number} x
     * @param {BryntumScrollOptions|Boolean} [options] How to scroll. May be passed as `true` to animate.
     * @returns {Promise} A promise which is resolved when the scrolling has finished.
     * @category Scrolling
     */
    scrollHorizontallyTo(coordinate, options = true) {
      return this.timeAxisSubGrid.scrollable.scrollTo(coordinate, null, options);
    }
    /**
     * Vertical scrolling
     * @param {Number} y
     * @param {BryntumScrollOptions|Boolean} [options] How to scroll. May be passed as `true` to animate.
     * @returns {Promise} A promise which is resolved when the scrolling has finished.
     * @category Scrolling
     */
    scrollVerticallyTo(y, options = true) {
      return this.scrollable.scrollTo(null, y, options);
    }
    /**
     * Scrolls the subgrid that contains the scheduler
     * @param {Number} x
     * @param {BryntumScrollOptions|Boolean} [options] How to scroll. May be passed as `true` to animate.
     * @returns {Promise} A promise which is resolved when the scrolling has finished.
     * @category Scrolling
     */
    scrollTo(x, options = true) {
      return this.timeAxisSubGrid.scrollable.scrollTo(x, null, options);
    }
    //endregion
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "TimelineScroll"), __publicField(_a5, "configurable", {
    /**
     * This config defines the size of the start and end invisible parts of the timespan when {@link #config-infiniteScroll} set to `true`.
     *
     * It should be provided as a coefficient, which will be multiplied by the size of the scheduling area.
     *
     * For example, if `bufferCoef` is `5` and the panel view width is 200px then the timespan will be calculated to
     * have approximately 1000px (`5 * 200`) to the left and 1000px to the right of the visible area, resulting
     * in 2200px of totally rendered content.
     *
     * @config {Number}
     * @category Infinite scroll
     * @advanced
     * @default
     */
    bufferCoef: 5,
    /**
     * This config defines the scroll limit, which, when exceeded will cause a timespan shift.
     * The limit is calculated as the `panelWidth * {@link #config-bufferCoef} * bufferThreshold`. During scrolling, if the left or right side
     * has less than that of the rendered content - a shift is triggered.
     *
     * For example if `bufferCoef` is `5` and the panel view width is 200px and `bufferThreshold` is 0.2, then the timespan
     * will be shifted when the left or right side has less than 200px (5 * 200 * 0.2) of content.
     * @config {Number}
     * @category Infinite scroll
     * @advanced
     * @default
     */
    bufferThreshold: 0.2,
    /**
     * Set to `true` to automatically adjust the panel timespan during scrolling in the time dimension,
     * when the scroller comes close to the start/end edges.
     *
     * The actually rendered timespan in this mode (and thus the amount of HTML in the DOM) is calculated based
     * on the {@link #config-bufferCoef} option, and is thus not controlled by the {@link Scheduler/view/TimelineBase#config-startDate}
     * and {@link Scheduler/view/TimelineBase#config-endDate} configs. The moment when the timespan shift
     * happens is determined by the {@link #config-bufferThreshold} value.
     *
     * To specify initial point in time to view, supply the
     * {@link Scheduler/view/TimelineBase#config-visibleDate} config.
     *
     * {@note}If you activate {@link Scheduler.view.Scheduler#lazy-loading-data-infinite-scroll lazy loading}
     * you get an infinitely scrollable timeline with lazy loading of content.{/@note}
     *
     * @prp {Boolean} infiniteScroll
     * @category Infinite scroll
     * @default
     */
    infiniteScroll: false
  }), _a5;
};
var TimelineScroller = class extends Scroller {
  static get configurable() {
    return {
      position: null,
      x: null,
      y: null
    };
  }
  // This class is passive about configuring the element.
  // It has no opinions about *how* the overflow is handled.
  updateOverflowX() {
  }
  updateOverflowY() {
  }
  onScroll(e) {
    super.onScroll(e);
    this._position = null;
  }
  syncPartners(force) {
    this.scrollable.syncPartners(force);
  }
  updatePosition(position) {
    this.scrollable[this.isHorizontal ? "x" : "y"] = position;
  }
  get viewport() {
    return this.scrollable.viewport;
  }
  get position() {
    return this._position = this.scrollable[this.isHorizontal ? "x" : "y"];
  }
  get clientSize() {
    return this.scrollable[`client${this.isHorizontal ? "Width" : "Height"}`];
  }
  get scrollSize() {
    return this.scrollable[`scroll${this.isHorizontal ? "Width" : "Height"}`];
  }
  get maxPosition() {
    return this.scrollable[`max${this.isHorizontal ? "X" : "Y"}`];
  }
  scrollTo(position, options) {
    return this.isHorizontal ? this.scrollable.scrollTo(position, null, options) : this.scrollable.scrollTo(null, position, options);
  }
  scrollBy(xDelta = 0, yDelta = 0, options = defaultScrollOptions4) {
    return this.isHorizontal ? this.scrollable.scrollBy(xDelta || yDelta, 0, options) : this.scrollable.scrollBy(0, yDelta || xDelta, options);
  }
  scrollIntoView() {
    return this.scrollable.scrollIntoView(...arguments);
  }
  // We accommodate mistakes. Setting X and Y sets the appropriate scroll axis position
  changeX(x) {
    this.position = x;
  }
  changeY(y) {
    this.position = y;
  }
  get x() {
    return this.position;
  }
  set x(x) {
    this.scrollable[this.isHorizontal ? "x" : "y"] = x;
  }
  get y() {
    return this.position;
  }
  set y(y) {
    this.scroller[this.isHorizontal ? "x" : "y"] = y;
  }
  get clientWidth() {
    return this.clientSize;
  }
  get clientHeight() {
    return this.clientSize;
  }
  get scrollWidth() {
    return this.scrollSize;
  }
  get scrollHeight() {
    return this.scrollSize;
  }
  get maxX() {
    return this.maxPosition;
  }
  get maxY() {
    return this.maxPosition;
  }
};

// ../Scheduler/lib/Scheduler/view/mixin/TimelineState.js
var copyProperties = [
  "barMargin"
];
var TimelineState_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    /**
     * Gets or sets timeline's state. Check out {@link Scheduler.view.mixin.TimelineState} mixin for details.
     * @member {TimelineStateInfo} state
     */
    /**
     * Get timeline's current state for serialization. See {@link #property-state} for a list of the state variables.
     * @returns {Object} State object to be serialized
     * @private
     */
    getState() {
      const me = this, state = ObjectHelper.copyProperties(super.getState(), me, copyProperties);
      state.zoomLevel = me.zoomLevel;
      state.zoomLevelOptions = {
        startDate: me.startDate,
        endDate: me.endDate,
        // With infinite scroll reading viewportCenterDate too early will lead to exception
        centerDate: !me.infiniteScroll || me.timeAxisViewModel.availableSpace ? me.viewportCenterDate : void 0,
        width: me.tickSize
      };
      return state;
    }
    /**
     * Apply previously stored state.
     * @param {Object} state
     * @private
     */
    applyState(state) {
      var _a6;
      const me = this;
      state = Object.assign({}, state);
      me.suspendRefresh();
      if (state.zoomLevel != null) {
        delete state.startDate;
        delete state.endDate;
        me.timeAxis.suspendEvents();
      }
      const { zoomLevel, zoomLevelOptions } = state;
      delete state.zoomLevel;
      delete state.zoomLevelOptions;
      super.applyState(state);
      if (zoomLevel != null) {
        me.timeAxis.resumeEvents();
        if (me.infiniteScroll && ((_a6 = state == null ? void 0 : state.scroll) == null ? void 0 : _a6.scrollLeft)) {
          state.scroll.scrollLeft = {};
        }
        me.whenVisible(() => me.zoomToLevel(zoomLevel, zoomLevelOptions));
      }
      me.resumeRefresh(true, false);
    }
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "TimelineState"), _a5;
};

// ../Scheduler/lib/Scheduler/view/Header.js
var Header2 = class extends Header {
  refreshContent() {
    var _a5;
    if (!((_a5 = this.headersElement) == null ? void 0 : _a5.querySelector(".b-sch-timeaxiscolumn"))) {
      super.refreshContent();
    }
  }
};
__publicField(Header2, "$name", "SchedulerHeader");
Header2._$name = "Header";

// ../Scheduler/lib/Scheduler/view/TimeAxisSubGrid.js
var TimeAxisSubGrid = class extends SubGrid {
  static get configurable() {
    return {
      // A Scheduler's SubGrid doesn't accept external columns moving in
      sealedColumns: true,
      // Use Scheduler's Header class
      headerClass: Header2
    };
  }
  startConfigure(config) {
    const { grid: scheduler } = config;
    scheduler.timeAxisSubGrid = this;
    super.startConfigure(config);
    config.header = {
      cls: {
        "b-sticky-headers": scheduler.stickyHeaders
      }
    };
    delete config.headerClass;
    if (!("flex" in config || "width" in config)) {
      config.flex = 1;
    }
  }
  changeScrollable() {
    const me = this, scrollable = super.changeScrollable(...arguments);
    if (scrollable) {
      Object.defineProperty(scrollable, "scrollWidth", {
        get() {
          var _a5, _b2;
          return (_b2 = (_a5 = this.element) == null ? void 0 : _a5.scrollWidth) != null ? _b2 : 0;
        },
        set() {
          me.positionDirty = true;
          me.grid.updateCanvasSize();
        }
      });
    }
    return scrollable;
  }
  handleHorizontalScroll(addCls = true) {
    if (!this.grid._viewPresetChanging) {
      super.handleHorizontalScroll(addCls);
    }
  }
  /**
   * This is an event handler triggered when the TimeAxisSubGrid changes size.
   * Its height changes when content height changes, and that is not what we are
   * interested in here. If the *width* changes, that means the visible viewport
   * has changed size.
   * @param {HTMLElement} element
   * @param {Number} width
   * @param {Number} height
   * @param {Number} oldWidth
   * @param {Number} oldHeight
   * @private
   */
  onInternalResize(element, width, height, oldWidth, oldHeight) {
    const me = this;
    super.onInternalResize(...arguments);
    if (me.isPainted && width !== oldWidth) {
      const scheduler = me.grid, bodyHeight = scheduler._bodyRectangle.height, shouldSuspend = me.monitorResize && DomHelper.scrollBarWidth && width < oldWidth;
      if (shouldSuspend) {
        me.monitorResize = false;
      }
      scheduler.onSchedulerViewportResize(width, bodyHeight, oldWidth, bodyHeight);
      if (shouldSuspend) {
        me.queueMicrotask(() => me.monitorResize = true);
      }
    }
  }
  get headerScrollWidth() {
    return this.grid.isVertical ? super.headerScrollWidth : this.grid.timeAxisViewModel.totalSize;
  }
  // When restoring state we need to update time axis size immediately, resize event is not triggered fast enough to
  // restore center date consistently
  clearWidthCache() {
    super.clearWidthCache();
    if (this.owner.isHorizontal) {
      this.owner.updateViewModelAvailableSpace(this.width);
    }
  }
  async expand(animated = false, calledFromPartner = false) {
    const { owner } = this;
    await super.expand(animated, calledFromPartner);
    if (owner.isPainted) {
      owner.timeAxisViewModel.update(this.width, false, true);
    }
  }
};
__publicField(TimeAxisSubGrid, "$name", "TimeAxisSubGrid");
// Factoryable type name
__publicField(TimeAxisSubGrid, "type", "timeaxissubgrid");
TimeAxisSubGrid.initClass();
TimeAxisSubGrid._$name = "TimeAxisSubGrid";

// ../Scheduler/lib/Scheduler/view/TimelineBase.js
var inRange = (v, r0, r1) => r0 == null ? r1 == null || v < r1 : r1 == null ? v >= r0 : r0 < r1 ? r0 <= v && v < r1 : v < r1 || r0 <= v;
var isWorkingTime = (d, wt) => inRange(d.getDay(), wt.fromDay, wt.toDay) && inRange(d.getHours(), wt.fromHour, wt.toHour);
var emptyObject11 = {};
var TimelineBase = class extends GridBase.mixin(
  TimelineDateMapper_default,
  TimelineDomEvents_default,
  TimelineEventRendering_default,
  TimelineEarlyPositioning_default,
  TimelineZoomable_default,
  TimelineScroll_default,
  TimelineState_default,
  TimelineViewPresets_default,
  RecurringEvents_default
) {
  constructor() {
    super(...arguments);
    __publicField(this, "timeCellSelector", null);
  }
  static get defaultConfig() {
    return {
      /**
       * A valid JS day index between 0-6 (0: Sunday, 1: Monday etc.) to be considered the start day of the week.
       * When omitted, the week start day is retrieved from the active locale class.
       * @config {Number} weekStartDay
       * @category Time axis
       */
      /**
       * An object with format `{ fromDay, toDay, fromHour, toHour }` that describes the working days and hours.
       * This object will be used to populate TimeAxis {@link Scheduler.data.TimeAxis#config-include} property.
       *
       * Using it results in a non-continuous time axis. Any ticks not covered by the working days and hours will
       * be excluded. Events within larger ticks (for example if using week as the unit for ticks) will be
       * stretched to fill the gap otherwise left by the non-working hours.
       *
       * As with end dates, `toDay` and `toHour` are exclusive. Thus `toDay : 6` means that day 6 (saturday) will
       * not be included.
       *
       *
       * **NOTE:** When this feature is enabled {@link Scheduler.view.mixin.TimelineZoomable Zooming feature} is
       * not supported. It's recommended to disable zooming controls:
       *
       * ```javascript
       * new Scheduler({
       *     zoomOnMouseWheel          : false,
       *     zoomOnTimeAxisDoubleClick : false,
       *     ...
       * });
       * ```
       *
       * @prp {Object} workingTime
       * @property {Number} workingTime.fromDay Day index 0-6 (0 is Sunday)
       * @property {Number} workingTime.toDay Day index 0-6 (0 is Sunday)
       * @property {Number} workingTime.fromHour 0-23
       * @property {Number} workingTime.toHour 0-23
       * @category Time axis
       */
      workingTime: null,
      /**
       * A backing data store of 'ticks' providing the input date data for the time axis of timeline panel.
       * @member {Scheduler.data.TimeAxis} timeAxis
       * @readonly
       * @category Time axis
       */
      /**
       * A {@link Scheduler.data.TimeAxis} config object or instance, used to create a backing data store of
       * 'ticks' providing the input date data for the time axis of timeline panel. Created automatically if none
       * supplied.
       * @config {TimeAxisConfig|Scheduler.data.TimeAxis}
       * @category Time axis
       */
      timeAxis: null,
      /**
       * The backing view model for the visual representation of the time axis.
       * Either a real instance or a simple config object.
       * @private
       * @config {Scheduler.view.model.TimeAxisViewModel|TimeAxisViewModelConfig}
       * @category Time axis
       */
      timeAxisViewModel: null,
      /**
       * You can set this option to `false` to make the timeline panel start and end on the exact provided
       * {@link #config-startDate}/{@link #config-endDate} w/o adjusting them.
       * @config {Boolean}
       * @default
       * @category Time axis
       */
      autoAdjustTimeAxis: true,
      /**
       * Affects drag drop and resizing of events when {@link Scheduler/view/mixin/TimelineDateMapper#config-snap}
       * is enabled.
       *
       * If set to `true`, dates will be snapped relative to event start. e.g. for a zoom level with
       * `timeResolution = { unit: "s", increment: "20" }`, an event that starts at 10:00:03 and is dragged would
       * snap its start date to 10:00:23, 10:00:43 etc.
       *
       * When set to `false`, dates will be snapped relative to the timeAxis startDate (tick start)
       * - 10:00:03 -> 10:00:20, 10:00:40 etc.
       *
       * @config {Boolean}
       * @default
       * @category Scheduled events
       */
      snapRelativeToEventStartDate: false,
      /**
       * Set to `true` to prevent auto calculating of a minimal {@link Scheduler.view.mixin.TimelineEventRendering#property-tickSize}
       * to always fit the content to the screen size. Setting this property on `true` will disable {@link #config-forceFit} behaviour.
       * @prp {Boolean}
       * @default false
       * @category Time axis
       */
      suppressFit: false,
      /**
       * CSS class to add to cells in the timeaxis column
       * @config {String}
       * @category CSS
       * @private
       */
      timeCellCls: null,
      scheduledEventName: null,
      //dblClickTime : 200,
      /**
       * A CSS class to apply to each event in the view on mouseover.
       * @config {String}
       * @category CSS
       * @private
       */
      overScheduledEventClass: null,
      // allow the panel to prevent adding the hover CSS class in some cases - during drag drop operations
      preventOverCls: false,
      /**
       * Set to `false` if you don't want event bar DOM updates to animate.
       * @prp {Boolean}
       * @default true
       * @category Scheduled events
       */
      enableEventAnimations: true,
      disableGridRowModelWarning: true,
      // does not look good with locked columns and also interferes with event animations
      animateRemovingRows: false,
      schedulerRegion: "normal",
      transitionDuration: 200,
      // internal timer id reference
      animationTimeout: null,
      /**
       * Region to which columns are added when they have none specified
       * @config {String}
       * @default
       * @category Misc
       */
      defaultRegion: "locked",
      /**
       * Decimal precision used when displaying durations, used by tooltips and DurationColumn.
       * Specify `false` to use raw value
       * @config {Number|Boolean}
       * @default
       * @category Common
       */
      durationDisplayPrecision: 1,
      asyncEventSuffix: "PreCommit"
    };
  }
  updateTimeZone(timeZone) {
    if (this.project) {
      if (this.isConfiguring) {
        this.project._isConfiguringTimeZone = true;
      }
      this.project.timeZone = timeZone;
    }
  }
  get timeZone() {
    var _a5;
    return (_a5 = this.project) == null ? void 0 : _a5.timeZone;
  }
  get hasTimelineBoundaries() {
    return Boolean(this.minDate || this.maxDate);
  }
  //endregion
  //region Feature hooks
  /**
   * Populates the event context menu. Chained in features to add menu items.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown.
   * @param {Scheduler.model.EventModel} options.eventRecord The context event.
   * @param {Scheduler.model.ResourceModel} options.resourceRecord The context resource.
   * @param {Scheduler.model.AssignmentModel} options.assignmentRecord The context assignment if any.
   * @param {Object<String,MenuItemConfig|Boolean|null>} options.items A named object to describe menu items.
   * @internal
   */
  populateEventMenu() {
  }
  /**
   * Populates the time axis context menu. Chained in features to add menu items.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown.
   * @param {Scheduler.model.ResourceModel} options.resourceRecord The context resource.
   * @param {Date} options.date The Date corresponding to the mouse position in the time axis.
   * @param {Object<String,MenuItemConfig|Boolean|null>} options.items A named object to describe menu items.
   * @internal
   */
  populateScheduleMenu() {
  }
  // Called when visible date range potentially changes such as when scrolling in
  // the time axis.
  internalOnVisibleDateRangeChange(range) {
    if (!this.handlingVisibleDateRangeChange) {
      const me = this, { _visibleDateRange } = me, dateRangeChange = !_visibleDateRange || (_visibleDateRange.startDate - range.startDate || _visibleDateRange.endDate - range.endDate);
      if (dateRangeChange) {
        me.timeView.range = range;
        me.handlingVisibleDateRangeChange = true;
        me.trigger("visibleDateRangeChange", {
          old: _visibleDateRange,
          new: range
        });
        me.handlingVisibleDateRangeChange = false;
        me._visibleDateRange = range;
      }
    }
  }
  // Called when visible resource range changes in vertical mode
  onVisibleResourceRangeChange() {
  }
  // Called when visible resource column changes in vertical mode
  onVisibleResourceColumnChange() {
  }
  //endregion
  //region Init
  construct(config = {}) {
    const me = this;
    super.construct(config);
    me.$firstVerticalOverflow = true;
    me.initDomEvents();
    me.currentOrientation.init();
    me.rowManager.ion({
      refresh: () => {
        me.forceLayout = false;
      }
    });
  }
  // Override from Grid.view.GridSubGrids
  createSubGrid(region, config = {}) {
    const me = this, { stickyHeaders } = me;
    if (region === (me.schedulerRegion || "normal")) {
      config.type = "timeaxissubgrid";
    } else if (region === "locked" && stickyHeaders && me.isVertical) {
      config.scrollable = {
        overflowX: "visible",
        overflowY: "visible"
      };
      if (!("resizable" in config)) {
        config.resizable = false;
      }
      me.bodyContainer.classList.add("b-sticky-headers");
    }
    return super.createSubGrid(region, config);
  }
  doDestroy() {
    const me = this, { partneredWith, currentOrientation } = me;
    currentOrientation == null ? void 0 : currentOrientation.destroy();
    if (me._bodyContainer) {
      ResizeMonitor.removeResizeListener(me._bodyContainer, me.onBodyResize);
    }
    if (partneredWith) {
      for (let i = partneredWith.count - 1; i > -1; --i) {
        const partner = partneredWith.getAt(i);
        me.removePartner(partner);
      }
      partneredWith.destroy();
    } else {
      me.timeAxisViewModel.destroy();
      me.timeAxis.destroy();
    }
    super.doDestroy();
  }
  startConfigure(config) {
    const me = this;
    super.startConfigure(config);
    me.onBodyResize = me.onBodyResize.bind(me);
    if (me._bodyContainer) {
      ResizeMonitor.addResizeListener(me._bodyContainer, me.onBodyResize);
    }
    me.getConfig("partner");
  }
  onInternalPaint({ firstPaint }) {
    var _a5, _b2;
    if (firstPaint) {
      const me = this, scrollable = me.isHorizontal ? me.timeAxisSubGrid.scrollable : me.scrollable, availableSpace = scrollable.element.getBoundingClientRect()[me.isHorizontal ? "width" : "height"];
      me.timeAxisViewModel.update(availableSpace, me.infiniteScroll, true);
      if (me.infiniteScroll) {
        (_b2 = (_a5 = me.currentOrientation).doUpdateTimeView) == null ? void 0 : _b2.call(_a5);
      }
      me.getConfig("hideRowHover");
    }
    super.onInternalPaint(...arguments);
  }
  onSchedulerHorizontalScroll(subGrid, scrollLeft, scrollX, scrollingToCenter) {
    this.currentOrientation.updateFromHorizontalScroll(scrollX);
    super.onSchedulerHorizontalScroll(subGrid, scrollLeft, scrollX, scrollingToCenter);
  }
  /**
   * Overrides initScroll from Grid, listens for horizontal scroll to do virtual event rendering
   * @private
   */
  initScroll() {
    const me = this;
    let frameCount = 0;
    super.initScroll();
    me.ion({
      horizontalScroll: ({ subGrid, scrollLeft, scrollX, scrollingToCenter }) => {
        if (me.isPainted && subGrid === me.timeAxisSubGrid && !me.isDestroying && !me.refreshSuspended) {
          me.onSchedulerHorizontalScroll(subGrid, scrollLeft, scrollX, scrollingToCenter);
        }
        frameCount++;
      }
    });
    if (me.testPerformance === "horizontal") {
      me.setTimeout(() => {
        const start = performance.now();
        let scrollSpeed = 5, direction = 1;
        const scrollInterval = me.setInterval(() => {
          scrollSpeed = scrollSpeed + 5;
          me.scrollX += (10 + Math.floor(scrollSpeed)) * direction;
          if (direction === 1 && me.scrollX > 5500) {
            direction = -1;
            scrollSpeed = 5;
          }
          if (direction === -1 && me.scrollX <= 0) {
            const done = performance.now(), elapsed = done - start;
            const timePerFrame = elapsed / frameCount, fps = Math.round(1e3 / timePerFrame * 10) / 10;
            clearInterval(scrollInterval);
            console.log(me.eventPositionMode, me.eventScrollMode, fps + "fps");
          }
        }, 0);
      }, 500);
    }
  }
  //endregion
  /**
   * Calls the specified function (returning its return value) and preserves the timeline center
   * point. This is a useful way of retaining the user's visual context while making updates
   * and changes to the view which require major changes or a full refresh.
   * @param {Function} fn The function to call.
   * @param {Object} thisObj The `this` context for the function.
   * @param {...*} args Parameters to the function.
   */
  preserveViewCenter(fn2, thisObj = this, ...args) {
    const me = this, centerDate = me.viewportCenterDate, result = fn2.apply(thisObj, args), scroller = me.timelineScroller, { clientSize } = scroller, scrollStart = Math.max(Math.floor(me.getCoordinateFromDate(centerDate, true) - clientSize / 2), 0);
    scroller.scrollTo(scrollStart, { scrollingToCenter: true });
    return result;
  }
  /**
   * Changes the Scheduler's time axis timespan to the supplied start and end dates.
   * @param {Date} newStartDate The new start date
   * @param {Date} [newEndDate] The new end date. If omitted or equal to startDate, the
   * {@link Scheduler.preset.ViewPreset#field-defaultSpan} property of the current view preset will be used to calculate the new end date.
   * @param {Object} [options] An object containing modifiers for the time span change operation.
   * @param {Boolean} [options.maintainVisibleStart] Specify as `true` to keep the visible start date stable.
   * @param {Date} [options.visibleDate] The date inside the range to scroll into view
   * @async
   */
  setTimeSpan(newStartDate, newEndDate, options = emptyObject11) {
    const me = this, { timeAxis } = me, {
      preventThrow = false,
      // Private, only used by the shift method.
      maintainVisibleStart: maintainVisibleStart2 = false,
      visibleDate
    } = options, {
      startDate,
      endDate
    } = timeAxis.getAdjustedDates(newStartDate, newEndDate), startChanged = timeAxis.startDate - startDate !== 0, endChanged = timeAxis.endDate - endDate !== 0;
    if (startChanged || endChanged) {
      if (maintainVisibleStart2) {
        const {
          timeAxisViewModel
        } = me, { totalSize } = timeAxisViewModel, oldTickSize = timeAxisViewModel.tickSize, scrollable = me.timelineScroller, currentScroll = scrollable.position, visibleStart = timeAxisViewModel.getDateFromPosition(currentScroll);
        if (visibleStart >= startDate && visibleStart < endDate) {
          timeAxisViewModel.ion({
            update() {
              const tickSizeChanged = timeAxisViewModel.tickSize !== oldTickSize;
              me.updateCanvasSize();
              if (startChanged && !endChanged && !tickSizeChanged) {
                scrollable.position += timeAxisViewModel.totalSize - totalSize;
              } else if (!startChanged && !tickSizeChanged) {
                scrollable.position = currentScroll;
              } else {
                scrollable.position = timeAxisViewModel.getPositionFromDate(visibleStart);
              }
              scrollable.syncPartners(true);
            },
            prio: 1e4,
            once: true
          });
        }
      }
      if (me.lastViewPresetOptions) {
        me.lastViewPresetOptions = { startDate, endDate };
      }
      const returnValue = timeAxis.reconfigure({
        startDate,
        endDate
      }, false, preventThrow);
      if (visibleDate) {
        return me.scrollToDate(visibleDate, options).then(() => returnValue);
      }
      return returnValue;
    }
  }
  //region Config getters/setters
  /**
   * Returns the TimeAxisColumn instance
   * @member {Scheduler.column.TimeAxisColumn|Scheduler.column.VerticalTimeAxisColumn} timeAxisColumn
   * @readonly
   * @category Time axis
   */
  /**
   * Returns `true` if any of the events/tasks or feature injected elements (such as ResourceTimeRanges) are within
   * the {@link #config-timeAxis}
   * @property {Boolean}
   * @readonly
   * @category Scheduled events
   */
  get hasVisibleEvents() {
    return !this.noFeatureElementsInAxis() || this.eventStore.storage.values.some((t) => this.timeAxis.isTimeSpanInAxis(t));
  }
  // Template function to be chained in features to determine if any elements are in time axis (needed since we cannot
  // currently chain getters). Negated to not break chain. First feature that has elements visible returns false,
  // which prevents other features from being queried.
  noFeatureElementsInAxis() {
  }
  // Private getter used to piece together event names such as beforeEventDrag / beforeTaskDrag. Could also be used
  // in templates.
  get capitalizedEventName() {
    if (!this._capitalizedEventName) {
      this._capitalizedEventName = StringHelper.capitalize(this.scheduledEventName);
    }
    return this._capitalizedEventName;
  }
  updatePartner(partner) {
    if (partner) {
      this.addPartner(partner);
    }
  }
  /**
   * Partners this Timeline with the passed Timeline in order to sync the horizontal scrolling position and zoom level.
   *
   * - To remove existing partner see {@link #function-removePartner} method.
   * - To get the list of partners see {@link #property-partners} getter.
   *
   * The following properties are imported into this component from the added partner and shared:
   *
   * - {@link #property-timeAxisViewModel}
   * - {@link #property-timeAxis}
   * - {@link #property-viewPreset}
   *
   * In a set of partnered Timelines, there will only be a single instance of the above properties.
   * The time range and scroll position in that time range are always the same among all
   * partnered timelines.
   *
   * @param {Scheduler.view.TimelineBase} otherTimeline The timeline to partner with
   */
  addPartner(partner) {
    const me = this;
    if (!me.isPartneredWith(partner)) {
      if (me._partner == null) {
        me._partner = partner;
      }
      const partneredWith = me.partneredWith || (me.partneredWith = new Collection());
      partneredWith.add(partner);
      (partner.partneredWith || (partner.partneredWith = new Collection())).add(me);
      me.getConfig("viewPreset");
      me.getConfig("infiniteScroll");
      partner.ion({
        presetchange: "onPartnerPresetChange",
        thisObj: me
      });
      const partnerSharedConfig = me.partnerSharedConfigs.reduce((config, configName) => {
        config[configName] = partner[configName];
        return config;
      }, {});
      partnerSharedConfig.viewPreset.options = {
        scrollPosition: partner.timelineScroller.position,
        tickSize: partner.tickSize
      };
      me._ownTimeAxis = me.timeAxis;
      me._ownTimeAxisViewModel = me.timeAxisViewModel;
      me.setConfig(partnerSharedConfig);
      me.ion({
        presetchange: "onPartnerPresetChange",
        thisObj: partner
      });
      if (me.isPainted) {
        partner.scrollable.addPartner(me.scrollable, me.isHorizontal ? "x" : "y");
        partner.syncPartnerSubGrids();
      } else {
        me.initScroll = FunctionHelper.createSequence(me.initScroll, () => {
          partner.scrollable.addPartner(me.scrollable, me.isHorizontal ? "x" : "y");
          partner.syncPartnerSubGrids();
        }, me);
      }
    }
  }
  /**
   * Breaks the link between current Timeline and the passed Timeline
   *
   * - To add a new partner see {@link #function-addPartner} method.
   * - To get the list of partners see {@link #property-partners} getter.
   *
   * @param {Scheduler.view.TimelineBase} otherTimeline The timeline to unlink from
   */
  removePartner(partner) {
    const me = this, { partneredWith, timeAxis } = me;
    if (me.isPartneredWith(partner)) {
      if (me._partner === partner) {
        me._partner = null;
      }
      partneredWith.remove(partner);
      me.scrollable.removePartner(partner.scrollable);
      me.un({
        presetchange: "onPartnerPresetChange",
        thisObj: partner
      });
      const { scrollLeft, scrollTop } = partner;
      partner.removePartner(me, true);
      if (timeAxis.owner !== me && !me.isDestroying) {
        me.timeAxis = {
          ...partner.timeAxis.getCurrentConfig({ skip: { owner: 1 } }),
          owner: me
        };
        const config = partner.timeAxisViewModel.getCurrentConfig({ skip: { owner: 1, timeAxis: 1, internalListeners: 1 } });
        me.timeAxisViewModel = {
          ...config,
          owner: me,
          timeAxis: me.timeAxis
        };
        me.scrollLeft = scrollLeft;
        me.scrollTop = scrollTop;
        me.timeAxisViewModel.update(me.timeAxisSubGridElement.getBoundingClientRect()[me.isHorizontal ? "width" : "height"]);
      }
    }
  }
  /**
   * Checks whether the passed timeline is partnered with the current timeline.
   * @param {Scheduler.view.TimelineBase} partner The timeline to check the partnering with
   * @returns {Boolean} Returns `true` if the timelines are partnered
   */
  isPartneredWith(partner) {
    var _a5;
    return Boolean((_a5 = this.partneredWith) == null ? void 0 : _a5.includes(partner));
  }
  onPartnerPresetChange({ preset, startDate, endDate, centerDate, zoomDate, zoomPosition, zoomLevel }) {
    if (!this._viewPresetChanging && this.viewPreset !== preset) {
      preset.options = {
        startDate,
        endDate,
        centerDate,
        zoomDate,
        zoomPosition,
        zoomLevel
      };
      this.viewPreset = preset;
    }
  }
  /**
   * Returns the partnered timelines.
   *
   * - To add a new partner see {@link #function-addPartner} method.
   * - To remove existing partner see {@link #function-removePartner} method.
   *
   * @readonly
   * @member {Scheduler.view.TimelineBase[]} partners
   * @category Time axis
   */
  get partners() {
    const partners = this.partner ? [this.partner] : [];
    if (this.partneredWith) {
      partners.push.apply(partners, this.partneredWith.allValues);
    }
    return [...new Set(partners)];
  }
  get timeAxisColumn() {
    return this.columns && this._timeAxisColumn;
  }
  async show() {
    await super.show(...arguments);
    if (this.partner) {
      this.partner.eachSubGrid((subGrid) => subGrid.scrollable.syncPartners());
    }
  }
  changeColumns(columns, currentStore) {
    const me = this;
    let timeAxisColumnIndex, { timeAxisColumnConfig } = me;
    if (columns) {
      const isArray4 = Array.isArray(columns);
      let cols = columns;
      if (!isArray4) {
        cols = columns.data;
      }
      timeAxisColumnIndex = cols && cols.length;
      cols.some((col, index) => {
        if (col.type === "timeAxis") {
          timeAxisColumnIndex = index;
          timeAxisColumnConfig = ObjectHelper.assign(col, me.timeAxisColumn);
          return true;
        }
        return false;
      });
      if (me.isVertical) {
        cols = [
          ObjectHelper.assign({
            type: "verticalTimeAxis"
          }, me.verticalTimeAxisColumn),
          // Make space for a regular TimeAxisColumn after the VerticalTimeAxisColumn
          cols[timeAxisColumnIndex]
        ];
        timeAxisColumnIndex = 1;
      } else {
        cols = cols.slice();
      }
      cols[timeAxisColumnIndex] = this._timeAxisColumn || ObjectHelper.assign({
        type: "timeAxis",
        cellCls: me.timeCellCls,
        mode: me.mode
      }, timeAxisColumnConfig);
      if (isArray4 || columns.isStore && columns.owner !== this) {
        columns = cols;
      } else {
        columns.data = cols;
      }
    }
    return super.changeColumns(columns, currentStore);
  }
  updateColumns(columns, was) {
    super.updateColumns(columns, was);
    if (columns) {
      const me = this, timeAxisColumn = me._timeAxisColumn = me.columns.find((c) => c.isTimeAxisColumn);
      if (me.isVertical) {
        me.verticalTimeAxisColumn = me.columns.find((c) => c.isVerticalTimeAxisColumn);
        me.verticalTimeAxisColumn.relayAll(me);
      }
      timeAxisColumn.relayAll(me);
    }
  }
  onColumnsChanged({ action, changes, record: column, records }) {
    var _a5;
    const { timeAxisColumn, columns } = this;
    if ((action === "dataset" || action === "batch") && !columns.includes(timeAxisColumn)) {
      columns.add(timeAxisColumn, true);
    } else if (column === timeAxisColumn && "width" in changes) {
      this.updateCanvasSize();
    }
    column && ((_a5 = this.partneredWith) == null ? void 0 : _a5.forEach((partner) => {
      const partnerColumn = partner.columns.getAt(column.allIndex);
      if (partnerColumn == null ? void 0 : partnerColumn.shouldSync(column)) {
        const partnerChanges = {};
        for (const k in changes) {
          partnerChanges[k] = changes[k].value;
        }
        partnerColumn.set(partnerChanges);
      }
    }));
    super.onColumnsChanged(...arguments);
  }
  get timeView() {
    var _a5, _b2;
    const me = this;
    return me.columns && me.isVertical ? (_a5 = me.verticalTimeAxisColumn) == null ? void 0 : _a5.view : (_b2 = me.timeAxisColumn) == null ? void 0 : _b2.timeAxisView;
  }
  updateEventCls(eventCls) {
    const me = this;
    if (!me.eventSelector) {
      me.unreleasedEventSelector = me.eventSelector = `.${eventCls}-wrap`;
    }
    if (!me.eventInnerSelector) {
      me.eventInnerSelector = `.${eventCls}`;
    }
  }
  set timeAxisViewModel(timeAxisViewModel) {
    var _a5;
    const me = this, currentModel = me._timeAxisViewModel, tavmListeners = {
      name: "timeAxisViewModel",
      update: "onTimeAxisViewModelUpdate",
      prio: 100,
      thisObj: me
    };
    if (me.isConfiguring && me.initialConfig.partner) {
      timeAxisViewModel = me.initialConfig.partner.timeAxisViewModel;
    }
    if (currentModel && currentModel === timeAxisViewModel) {
      return;
    }
    if ((currentModel == null ? void 0 : currentModel.owner) === me) {
      currentModel.destroy();
    }
    me.detachListeners("timeAxisViewModel");
    if (timeAxisViewModel == null ? void 0 : timeAxisViewModel.isTimeAxisViewModel) {
      timeAxisViewModel.ion(tavmListeners);
    } else {
      timeAxisViewModel = TimeAxisViewModel.new({
        mode: me._mode,
        snap: me.snap,
        forceFit: me.forceFit,
        timeAxis: me.timeAxis,
        suppressFit: me.suppressFit,
        internalListeners: tavmListeners,
        owner: me
      }, timeAxisViewModel);
    }
    me._timeAxisViewModel = timeAxisViewModel;
    if (me.isHorizontal && me.timeAxisColumn) {
      me.timeAxisColumn.timeAxisViewModel = timeAxisViewModel;
    } else if (me.isVertical && ((_a5 = me.verticalTimeAxisColumn) == null ? void 0 : _a5.view)) {
      me.verticalTimeAxisColumn.view.model = timeAxisViewModel;
    }
    me.relayEvents(timeAxisViewModel, ["update"], "timeAxisViewModel");
    if (currentModel && timeAxisViewModel) {
      me.trigger("timeAxisViewModelChange", { timeAxisViewModel });
    }
  }
  /**
   * The internal view model, describing the visual representation of the time axis.
   * @property {Scheduler.view.model.TimeAxisViewModel}
   * @readonly
   * @category Time axis
   */
  get timeAxisViewModel() {
    if (!this._timeAxisViewModel) {
      this.timeAxisViewModel = null;
    }
    return this._timeAxisViewModel;
  }
  get suppressFit() {
    var _a5, _b2;
    return (_b2 = (_a5 = this._timeAxisViewModel) == null ? void 0 : _a5.suppressFit) != null ? _b2 : this._suppressFit;
  }
  set suppressFit(value2) {
    if (this._timeAxisViewModel) {
      this.timeAxisViewModel.suppressFit = value2;
    } else {
      this._suppressFit = value2;
    }
  }
  set timeAxis(timeAxis) {
    const me = this, currentTimeAxis = me._timeAxis, timeAxisListeners = {
      name: "timeAxis",
      reconfigure: "onTimeAxisReconfigure",
      thisObj: me
    };
    if (me._partner && !timeAxis || currentTimeAxis && currentTimeAxis === timeAxis) {
      return;
    }
    if (currentTimeAxis) {
      if (currentTimeAxis.owner === me) {
        currentTimeAxis.destroy();
      }
    }
    me.detachListeners("timeAxis");
    if (!(timeAxis == null ? void 0 : timeAxis.isTimeAxis)) {
      timeAxis = ObjectHelper.assign({
        owner: me,
        viewPreset: me.viewPreset,
        autoAdjust: me.autoAdjustTimeAxis,
        weekStartDay: me.weekStartDay,
        forceFullTicks: me.fillTicks && me.snap
      }, timeAxis);
      if (me.startDate) {
        timeAxis.startDate = me.startDate;
      }
      if (me.endDate) {
        timeAxis.endDate = me.endDate;
      }
      if (me.workingTime) {
        me.applyWorkingTime(timeAxis);
      }
      timeAxis = new TimeAxis(timeAxis);
    }
    timeAxis.ion(timeAxisListeners);
    me._timeAxis = timeAxis;
  }
  onTimeAxisReconfigure({ config, oldConfig }) {
    if (config) {
      const dateRangeChange = !oldConfig || (oldConfig.startDate - config.startDate || oldConfig.endDate - config.endDate);
      if (dateRangeChange) {
        this.trigger("dateRangeChange", {
          old: {
            startDate: oldConfig.startDate,
            endDate: oldConfig.endDate
          },
          new: {
            startDate: config.startDate,
            endDate: config.endDate
          }
        });
      }
    }
    this.trigger("timeAxisChange", { config });
  }
  get timeAxis() {
    if (!this._timeAxis) {
      this.timeAxis = null;
    }
    return this._timeAxis;
  }
  updateForceFit(value2) {
    if (this._timeAxisViewModel) {
      this._timeAxisViewModel.forceFit = value2;
    }
  }
  set workingTime(config) {
    this._workingTime = config;
    if (!this.isConfiguring) {
      this.applyWorkingTime(this.timeAxis);
    }
  }
  get workingTime() {
    return this._workingTime;
  }
  // Translates the workingTime configs into TimeAxis#include rules, applies them and then refreshes the header and
  // redraws the events
  applyWorkingTime(timeAxis) {
    var _a5;
    const me = this, config = me._workingTime;
    if (config) {
      let hour2 = null;
      if (config.fromHour >= 0 && config.fromHour < 24 && config.toHour > config.fromHour && config.toHour <= 24 && config.toHour - config.fromHour < 24) {
        hour2 = { from: config.fromHour, to: config.toHour };
      }
      let day2 = null;
      if (config.fromDay >= 0 && config.fromDay < 7 && config.toDay > config.fromDay && config.toDay <= 7 && config.toDay - config.fromDay < 7) {
        day2 = { from: config.fromDay, to: config.toDay };
      }
      if (hour2 || day2) {
        timeAxis.include = {
          hour: hour2,
          day: day2
        };
      } else {
        timeAxis.include = null;
      }
    } else {
      timeAxis.include = null;
    }
    if (me.isPainted) {
      me.timeAxisColumn.refreshHeader();
      (_a5 = me.features.columnLines) == null ? void 0 : _a5.refresh();
      me.refreshWithTransition();
    }
  }
  changeMinDate(minDate) {
    if (typeof minDate === "string") {
      minDate = DateHelper.parse(minDate);
    } else if (!DateHelper.isDate(minDate)) {
      minDate = null;
    }
    return minDate;
  }
  updateMinDate(date2) {
    if (date2 && DateHelper.isBefore(this.startDate, date2)) {
      this.setStartDate(date2, false);
    }
  }
  isBeforeMinDate(date2) {
    return this.minDate && DateHelper.isBefore(date2, this.minDate);
  }
  getMinDateOrDate(date2) {
    if (this.minDate) {
      return DateHelper.max(this.minDate, date2);
    }
    return date2;
  }
  changeMaxDate(maxDate) {
    if (typeof maxDate === "string") {
      maxDate = DateHelper.parse(maxDate);
    } else if (!DateHelper.isDate(maxDate)) {
      maxDate = null;
    } else if (this.isBeforeMinDate(maxDate)) {
      maxDate = null;
    }
    return maxDate;
  }
  updateMaxDate(date2) {
    if (date2 && DateHelper.isBefore(date2, this.endDate)) {
      this.setEndDate(date2, false);
    }
  }
  isAfterMaxDate(date2) {
    return this.maxDate && DateHelper.isBefore(this.maxDate, date2);
  }
  getMaxDateOrDate(date2) {
    if (this.maxDate) {
      return DateHelper.min(this.maxDate, date2);
    }
    return date2;
  }
  changeStartDate(startDate) {
    if (typeof startDate === "string") {
      startDate = DateHelper.parse(startDate);
    }
    return startDate;
  }
  updateStartDate(date2) {
    this.setStartDate(date2);
  }
  /**
   * Sets the timeline start date.
   *
   * **Note:**
   * - If you need to set start and end date at the same time, use the {@link #function-setTimeSpan} method.
   * - If keepDuration is false and new start date is greater than end date, it will throw an exception.
   *
   * @param {Date} date The new start date
   * @param {Boolean} keepDuration Pass `true` to keep the duration of the timeline ("move" the timeline),
   * `false` to change the duration ("resize" the timeline). Defaults to `true`.
   */
  setStartDate(date2, keepDuration = true) {
    const me = this, ta = me._timeAxis, {
      startDate,
      endDate,
      mainUnit
    } = ta || emptyObject11;
    if (typeof date2 === "string") {
      date2 = DateHelper.parse(date2);
    }
    date2 = me.getMinDateOrDate(date2);
    if (ta && endDate) {
      if (date2) {
        let calcEndDate = endDate;
        if (keepDuration && startDate) {
          const diff = DateHelper.diff(startDate, endDate, mainUnit, true);
          calcEndDate = DateHelper.add(date2, diff, mainUnit);
        }
        me.setTimeSpan(date2, calcEndDate);
      }
    } else {
      me._tempStartDate = date2;
    }
  }
  get startDate() {
    var _a5;
    const me = this, { range, workingTime } = me;
    let ret = ((_a5 = me._timeAxis) == null ? void 0 : _a5.startDate) || me._tempStartDate;
    if (!ret) {
      ret = DateHelper.floor(me.peekConfig("date") || /* @__PURE__ */ new Date(), range);
      if (me.calendar) {
        me._tempEndDate = DateHelper.add(ret, range);
      }
      if (workingTime) {
        while (!isWorkingTime(ret, workingTime)) {
          ret.setHours(ret.getHours() + 1);
        }
      }
      me._tempStartDate = ret;
    }
    return ret;
  }
  changeEndDate(date2) {
    this.setEndDate(date2);
  }
  /**
   * Sets the timeline end date
   *
   * **Note:**
   * - If you need to set start and end date at the same time, use the {@link #function-setTimeSpan} method.
   * - If keepDuration is false and new end date is less than start date, it will throw an exception.
   *
   * @param {Date} date The new end date
   * @param {Boolean} keepDuration Pass `true` to keep the duration of the timeline ("move" the timeline),
   * `false` to change the duration ("resize" the timeline). Defaults to `false`.
   */
  setEndDate(date2, keepDuration = false) {
    const me = this, ta = me._timeAxis, {
      startDate,
      endDate,
      mainUnit
    } = ta || emptyObject11;
    if (typeof date2 === "string") {
      date2 = DateHelper.parse(date2);
    }
    date2 = me.getMaxDateOrDate(date2);
    if (ta && startDate) {
      if (date2) {
        let calcStartDate = startDate;
        if (keepDuration && endDate) {
          const diff = DateHelper.diff(startDate, endDate, mainUnit, true);
          calcStartDate = DateHelper.add(date2, -diff, mainUnit);
        }
        me.setTimeSpan(calcStartDate, date2);
      }
    } else {
      me._tempEndDate = date2;
    }
  }
  get endDate() {
    const me = this;
    if (me._timeAxis) {
      return me._timeAxis.endDate;
    }
    return me._tempEndDate || DateHelper.add(me.startDate, me.viewPreset.defaultSpan, me.viewPreset.mainHeader.unit);
  }
  changeVisibleDate(options) {
    if (typeof options === "string") {
      return this.changeVisibleDate({ date: options });
    }
    if (options instanceof Date) {
      return { date: options, block: this.isConfiguring ? "start" : "nearest" };
    }
    if (options instanceof Object) {
      return {
        ...options,
        date: typeof options.date === "string" ? DateHelper.parse(options.date) : options.date,
        block: options.block || (this.isConfiguring ? "start" : "nearest")
      };
    }
  }
  updateVisibleDate(options) {
    const me = this;
    if (!(me.infiniteScroll && me.isConfiguring)) {
      if (me.isPainted) {
        me.scrollToDate(options.date, options);
      } else if (!me.visibleDateProcessed) {
        me.whenVisible(() => me.updateVisibleDate(me.visibleDate));
        me.visibleDateProcessed = true;
      }
    }
  }
  get features() {
    return super.features;
  }
  // add region resize by default
  set features(features) {
    features = features === true ? {} : features;
    if (!("regionResize" in features)) {
      features.regionResize = true;
    }
    super.features = features;
  }
  //endregion
  //region Event handlers
  onLocaleChange() {
    super.onLocaleChange();
    const oldAutoAdjust = this.timeAxis.autoAdjust;
    this.timeAxis.reconfigure({
      autoAdjust: false
    });
    this.timeAxis.autoAdjust = oldAutoAdjust;
  }
  /**
   * Called when the element which encapsulates the Scheduler's visible height changes size.
   * We only respond to *height* changes here. The TimeAxisSubGrid monitors its own width.
   * @param {HTMLElement} element
   * @param {DOMRect} oldRect
   * @param {DOMRect} newRect
   * @private
   */
  onBodyResize(element, oldRect, { width, height }) {
    if (this.isVertical && oldRect && width !== oldRect.width) {
      delete this.timeAxisSubGrid._width;
    }
    const newWidth = this.timeAxisSubGrid.element.offsetWidth;
    if (this._bodyRectangle && oldRect && height !== oldRect.height) {
      this.onSchedulerViewportResize(newWidth, height, newWidth, oldRect.height);
    }
  }
  onSchedulerViewportResize(width, height, oldWidth, oldHeight) {
    if (this.isPainted) {
      const me = this, {
        isHorizontal,
        partneredWith
      } = me;
      me.currentOrientation.onViewportResize(width, height, oldWidth, oldHeight);
      me.updateViewModelAvailableSpace(isHorizontal ? width : Math.floor(height));
      if (partneredWith && !me.isSyncingFromPartner) {
        me.syncPartnerSubGrids();
      }
      me.trigger("timelineViewportResize", { width, height, oldWidth, oldHeight });
    }
  }
  updateViewModelAvailableSpace(space) {
    this.timeAxisViewModel.availableSpace = space;
  }
  onTimeAxisViewModelUpdate() {
    if (!this._viewPresetChanging && this.timeAxisSubGrid && !this.timeAxisSubGrid.collapsed) {
      this.updateCanvasSize();
      this.currentOrientation.onTimeAxisViewModelUpdate();
    }
  }
  syncPartnerSubGrids() {
    this.partneredWith.forEach((partner) => {
      if (!partner.isSyncingFromPartner) {
        partner.isSyncingFromPartner = true;
        this.eachSubGrid((subGrid) => {
          const partnerSubGrid = partner.subGrids[subGrid.region];
          if (partnerSubGrid.width !== subGrid.width) {
            if (subGrid.flex) {
              if (subGrid.flex !== partnerSubGrid.flex) {
                partnerSubGrid.flex = subGrid.flex;
              }
            } else if (partnerSubGrid.hasCalculatedWidth && subGrid.hasCalculatedWidth) {
              const width = Math.max(partnerSubGrid.totalFixedWidth, subGrid.totalFixedWidth);
              partnerSubGrid.width = subGrid.width = width;
              partnerSubGrid.hasCalculatedWidth = subGrid.hasCalculatedWidth = true;
            } else {
              partnerSubGrid.width = subGrid.width;
            }
          }
        });
        partner.isSyncingFromPartner = false;
      }
    });
  }
  //endregion
  //region Mode
  get currentOrientation() {
    throw new Error("Implement in subclass");
  }
  // Horizontal is the default, overridden in scheduler
  get isHorizontal() {
    return true;
  }
  //endregion
  //region Canvases and elements
  get foregroundCanvas() {
    return this._foregroundCanvas;
  }
  get svgCanvas() {
    const me = this;
    if (!me._svgCanvas) {
      const svg = me._svgCanvas = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("id", IdHelper.generateId("svg"));
      svg.classList.add("b-sch-canvas", "b-sch-dependencies-canvas");
      svg.retainElement = true;
      me.timeAxisSubGridElement.appendChild(svg);
      me.trigger("svgCanvasCreated", { svg });
    }
    return me._svgCanvas;
  }
  /**
   * Returns the subGrid containing the time axis
   * @member {Grid.view.SubGrid} timeAxisSubGrid
   * @readonly
   * @category Time axis
   */
  /**
   * Returns the html element for the subGrid containing the time axis
   * @property {HTMLElement}
   * @readonly
   * @category Time axis
   */
  get timeAxisSubGridElement() {
    var _a5;
    if (!this._timeAxisSubGridElement) {
      this.getConfig("regions");
      this._timeAxisSubGridElement = (_a5 = this.timeAxisColumn) == null ? void 0 : _a5.subGridElement;
    }
    return this._timeAxisSubGridElement;
  }
  updateCanvasSize() {
    const me = this;
    let result = false;
    if (me.isVertical) {
      const { totalSize } = me.timeAxisViewModel;
      if (me.isPainted) {
        me.refreshTotalHeight(totalSize + me._rowBorderHeight, true);
      }
      if (me.suppressFit) {
        DomHelper.setLength(me.foregroundCanvas, "height", totalSize);
      }
      result = true;
    }
    return result;
  }
  /**
   * A chainable function which Features may hook to add their own content to the timeaxis header.
   * @param {Array} configs An array of domConfigs, append to it to have the config applied to the header
   */
  getHeaderDomConfigs(configs) {
  }
  /**
   * A chainable function which Features may hook to add their own content to the foreground canvas
   * @param {Array} configs An array of domConfigs, append to it to have the config applied to the foreground canvas
   */
  getForegroundDomConfigs(configs) {
  }
  //endregion
  //region Grid overrides
  async onStoreDataChange({ action }) {
    var _a5;
    const me = this;
    if (me.isVisible) {
      if (action === "dataset" && ((_a5 = me.project) == null ? void 0 : _a5.isRepopulatingStores)) {
        await me.project.await("refresh", false);
      }
      super.onStoreDataChange(...arguments);
    } else {
      me.whenVisible("refresh", me, [true]);
    }
  }
  refresh(forceLayout = true) {
    const me = this;
    if (me.isPainted && !me.refreshSuspended) {
      if (me.isVertical || me.hasVisibleEvents || me.timeAxisSubGridElement.querySelector(me.eventSelector)) {
        if (!me.project || me.isEngineReady) {
          me.refreshRows(null, false, forceLayout);
        } else {
          me.refreshAfterProjectRefresh = true;
          me.currentOrientation.refreshAllWhenReady = true;
        }
      } else {
        me.rowManager.trigger("refresh");
      }
    }
  }
  render() {
    const me = this, { total } = me.resourceMarginObject, schedulerEl = me.timeAxisSubGridElement;
    const fgCanvas = me._foregroundCanvas = DomHelper.createElement({
      className: "b-sch-canvas b-sch-foreground-canvas",
      style: `font-size:${me.rowHeight - total}px`,
      parent: schedulerEl
    });
    me.canvases = {
      event: fgCanvas
    };
    me.timeAxisSubGrid.insertRowsBefore = fgCanvas;
    if (me.isVertical && me.suppressFit) {
      me.updateCanvasSize();
    }
    super.render(...arguments);
  }
  refreshRows(records = null, returnToTop = false, reLayoutEvents = true) {
    const me = this;
    if (me.isConfiguring) {
      return;
    }
    me.currentOrientation.refreshRows(records, reLayoutEvents);
    super.refreshRows(records, returnToTop);
  }
  updateHideHeaders(hide) {
    const me = this, scrollLeft = me.isPainted ? me.scrollLeft : 0;
    super.updateHideHeaders(hide);
    if (me.isPainted) {
      if (!hide) {
        me.timeAxisColumn.refreshHeader(null, true);
      }
      me.nextAnimationFrame().then(() => me.scrollLeft = scrollLeft);
    }
  }
  updateHideRowHover(hideRowHover) {
    this.timeAxisSubGridElement.classList.toggle("b-hide-row-hover", hideRowHover);
  }
  getCellDataFromEvent(event, includeSingleAxisMatch) {
    if (!event.target.closest(".b-verticaltimeaxis-cell")) {
      if (includeSingleAxisMatch) {
        includeSingleAxisMatch = !Boolean(event.target.closest(".b-sch-foreground-canvas"));
      }
      return super.getCellDataFromEvent(event, includeSingleAxisMatch);
    }
  }
  // This GridSelection override disables drag-selection in timeaxis column for scheduler and gantt
  onCellNavigate(me, from, to) {
    var _a5, _b2;
    const toTarget = to.target;
    if (((_a5 = to.cell) == null ? void 0 : _a5.classList.contains("b-timeaxis-cell")) && !((_b2 = GlobalEvents_default.currentMouseDown) == null ? void 0 : _b2.target.classList.contains("b-grid-cell"))) {
      this.preventDragSelect = true;
    }
    if ((!(toTarget == null ? void 0 : toTarget.matches(this.eventSelector)) || this.selectResourceOnEventNavigate !== false) && (!(toTarget == null ? void 0 : toTarget.matches(".b-timeaxis-cell")) || this.selectResourceOnScheduleClick !== false)) {
      super.onCellNavigate(...arguments);
    }
  }
  //endregion
  //region Other
  // Awaited by CellEdit to make sure that the editor is not moved until row heights have transitioned, to avoid it
  // ending up misaligned
  async waitForAnimations() {
    if (!this.isEngineReady && this.project) {
      await this.project.await("dataReady", false);
    }
    await super.waitForAnimations();
  }
  /**
   * Refreshes the grid with transitions enabled.
   */
  refreshWithTransition(forceLayout, shouldAnimate = true) {
    const me = this;
    if (!me.refreshSuspended && me.isPainted) {
      if (!me.rowManager.topRow) {
        me.rowManager.reinitialize();
      } else {
        me.runWithTransition(() => me.refresh(forceLayout), shouldAnimate);
      }
    }
  }
  /**
   * Returns an object representing the visible date range
   * @property {Object}
   * @property {Date} visibleDateRange.startDate
   * @property {Date} visibleDateRange.endDate
   * @readonly
   * @category Dates
   */
  get visibleDateRange() {
    return this.currentOrientation.visibleDateRange;
  }
  // This override will force row selection on timeaxis column selection, effectively disabling cell selection there
  isRowNumberSelecting(...selectors) {
    return super.isRowNumberSelecting(...selectors) || selectors.some((cs) => {
      var _a5;
      return cs.column ? cs.column.isTimeAxisColumn : (_a5 = cs.cell) == null ? void 0 : _a5.closest(".b-timeaxis-cell");
    });
  }
  //endregion
  /**
   * Returns a rounded duration value to be displayed in UI (tooltips, labels etc)
   * @param {Number} duration The raw duration value
   * @param {Number} [nbrDecimals] The number of decimals, defaults to {@link #config-durationDisplayPrecision}
   * @returns {Number} The rounded duration
   */
  formatDuration(duration, nbrDecimals = this.durationDisplayPrecision) {
    const multiplier = Math.pow(10, nbrDecimals);
    return Math.round(duration * multiplier) / multiplier;
  }
  beginListeningForBatchedUpdates() {
    var _a5;
    this.listenToBatchedUpdates = (this.listenToBatchedUpdates || 0) + 1;
    (_a5 = this.syncSplits) == null ? void 0 : _a5.call(this, (other) => other.beginListeningForBatchedUpdates());
  }
  endListeningForBatchedUpdates() {
    var _a5;
    if (this.listenToBatchedUpdates) {
      this.listenToBatchedUpdates -= 1;
    }
    (_a5 = this.syncSplits) == null ? void 0 : _a5.call(this, (other) => other.endListeningForBatchedUpdates());
  }
  onConnectedCallback(connected, initialConnect) {
    if (connected && !initialConnect) {
      this.timeAxisSubGrid.scrollable.x += 0.5;
    }
  }
  updateRtl(rtl) {
    const me = this, { isConfiguring } = me;
    let visibleDateRange;
    if (!isConfiguring) {
      visibleDateRange = me.visibleDateRange;
    }
    super.updateRtl(rtl);
    if (!isConfiguring) {
      me.currentOrientation.clearAll();
      if (me.infiniteScroll) {
        me.shiftToDate(visibleDateRange.startDate);
        me.scrollToDate(visibleDateRange.startDate, { block: "start" });
      } else {
        me.timelineScroller.position += 0.5;
      }
    }
  }
  /**
   * Applies the start and end date to each event store request (formatted in the same way as the start date field,
   * defined in the EventStore Model class).
   * @category Data
   * @private
   */
  applyStartEndParameters(params) {
    const me = this, field = me.eventStore.modelClass.fieldMap.startDate;
    if (me.passStartEndParameters) {
      params[me.startParamName] = field.print(me.startDate);
      params[me.endParamName] = field.print(me.endDate);
    }
  }
  intersectsVisibleTimeSpan(timespan) {
    return this.timeAxis.isTimeSpanInAxis(timespan);
  }
  /**
   * Highlights the elements of the passed event or assignment records (or record ids). If `scrollToClosest` is set to
   * `true` (which it is by default), the highlighted event element closest to the viewport center will be scrolled
   * into view.
   *
   * The highlighting will be done by adding the css class `b-highlighted` to the highlighted event elements. Also,
   * the css class `b-is-highlighting` will be added to the `TimeAxisSubGrid` element.
   * @param {Object} options Options config object
   * @param {Scheduler.model.EventModel|Scheduler.model.AssignmentModel|Number|String|Scheduler.model.EventModel[]|Scheduler.model.AssignmentModel[]|Number[]|String[]} options.events
   * The event records, assignment records or record ids to highlight. If providing ids, they will be interpreted as
   * event ids only if {@link Scheduler.data.EventStore#config-singleAssignment} is `true`, otherwise they will be
   * interpreted as assignment ids. If an empty array is passed, the `b-is-highlighting` class will be applied,
   * rendering all event elements in the style of `:not(.b-highlighted)`.
   * @param {Boolean|BryntumScrollOptions} [options.scroll] If `false`, no scrolling will be performed. If a
   * scrollOptions object, this object will be passed to the scroll function providing details on how to scroll.
   * @param {Boolean} [options.unhighlightOnClick] If `false`, a call to {@link #function-unhighlightEvents} is needed
   * to remove the highlighting. Otherwise, the highlighting will be removed when the user clicks somewhere in the
   * browser window.
   * @param {Boolean} [options.unhighlightOthers] If `true` this will automatically un-highlight the events not
   * present in the `events` property
   * @category Misc
   */
  highlightEvents({ events, scroll = true, unhighlightOnClick = true, unhighlightOthers = false }) {
    var _a5, _b2, _c2, _d;
    const me = this, { viewportCenterDate, rowManager } = me, highlighted = (_a5 = me._highlightedAssignments) != null ? _a5 : me._highlightedAssignments = [], assignments = me.internalGetAssignmentsToHighlight(events);
    let scrollToAssignment, lastDistance, calcDistance;
    if (scroll && assignments.length > 1) {
      const msPerPx = me.getMilliSecondsPerPixelForZoomLevel(me.viewPreset), { y, height } = me._bodyRectangle, centerY = y + height / 2, centerRowTop = (_c2 = (_b2 = rowManager.getRowAt(centerY)) != null ? _b2 : rowManager.bottomRow) == null ? void 0 : _c2.top;
      calcDistance = (assignment) => {
        const event = assignment.isTask ? assignment : assignment.event, resource = assignment.isTask ? assignment : assignment.resource, distanceMs = viewportCenterDate > event.endDate ? viewportCenterDate - event.endDate : event.startDate - viewportCenterDate, distanceTimeAxis = distanceMs / msPerPx, resourcePosition = me.mode === "vertical" ? me.variableColumnWidths ? resource.instanceMeta(me).insetStart : me.currentOrientation.allResourceRecords.indexOf(resource) * me.resourceColumnWidth : rowManager.getRecordCoords(resource).y, distanceResourceAxis = Math.abs(centerRowTop - resourcePosition);
        return Math.sqrt(distanceTimeAxis * distanceTimeAxis + distanceResourceAxis * distanceResourceAxis);
      };
    }
    if (unhighlightOthers && highlighted.length) {
      me.unhighlightEvents({ events: [...highlighted] });
    }
    assignments.forEach((assignment) => {
      if (!highlighted.includes(assignment)) {
        highlighted.push(assignment);
        me.currentOrientation.toggleCls(assignment, "b-highlighted", true);
        assignment.instanceMeta(me).isHighlighted = true;
      }
      let distance;
      if (scroll && me.store.includes(assignment.isTask ? assignment : assignment.resource)) {
        if (!calcDistance) {
          scrollToAssignment = assignment;
        } else if ((distance = calcDistance(assignment)) < lastDistance || lastDistance == null) {
          lastDistance = distance;
          scrollToAssignment = assignment;
        }
      }
    });
    me.timeAxisSubGridElement.classList.toggle("b-is-highlighting", true);
    if (scrollToAssignment) {
      me[`scroll${me.isGantt ? "Task" : "Assignment"}IntoView`](scrollToAssignment, scroll !== true ? scroll : void 0);
    }
    if (unhighlightOnClick && !me._highlightEventsListener) {
      me._highlightEventsListener = EventHelper.on({
        element: globalThis,
        autoDetach: true,
        thisObj: me,
        pointerdown: me.unhighlightEvents
      });
    }
    (_d = this.syncSplits) == null ? void 0 : _d.call(this, (other) => other.highlightEvents({ ...arguments[0], unhighlightOnClick: false }));
  }
  /**
   * Removes highlighting from the elements of the passed event or assignment records (or record ids). If no events
   * are passed all highlighted events will be un-highlighted.
   * @param {Object} [options] Options config object
   * @param {Scheduler.model.EventModel|Scheduler.model.AssignmentModel|Number|String|Scheduler.model.EventModel[]|Scheduler.model.AssignmentModel[]|Number[]|String[]} [options.events]
   * The event or assignment records or record ids to un-highlight. Omit this to un-highlight all.
   * @category Misc
   */
  unhighlightEvents(options) {
    var _a5;
    const me = this, { _highlightedAssignments } = me;
    let events = options == null ? void 0 : options.events;
    if (options instanceof Event) {
      const { target } = options;
      if (target.closest(".b-virtual-scroller") || me.bodyContainer === target && me.scrollable.hasOverflowY && (options.offsetX > target.clientWidth || !DomHelper.scrollBarWidth && options.offsetX > target.clientWidth - 15)) {
        return;
      }
      events = null;
    }
    if (_highlightedAssignments == null ? void 0 : _highlightedAssignments.length) {
      const assignments = events && me.internalGetAssignmentsToHighlight(events);
      for (let i = _highlightedAssignments.length - 1; i >= 0; i--) {
        const assignment = _highlightedAssignments[i];
        if (!assignments || assignments.includes(assignment)) {
          me.currentOrientation.toggleCls(assignment, "b-highlighted", false);
          assignment.instanceMeta(me).isHighlighted = false;
          _highlightedAssignments.splice(i, 1);
        }
      }
    }
    if (!(_highlightedAssignments == null ? void 0 : _highlightedAssignments.length)) {
      me.timeAxisSubGridElement.classList.toggle("b-is-highlighting", false);
    }
    if (me._highlightEventsListener) {
      me._highlightEventsListener();
      me._highlightEventsListener = null;
    }
    (_a5 = this.syncSplits) == null ? void 0 : _a5.call(this, (other) => other.unhighlightEvents(options));
  }
};
//region Config
/**
 * @hideproperties animateTreeNodeToggle
 */
__publicField(TimelineBase, "$name", "TimelineBase");
// Factoryable type name
__publicField(TimelineBase, "type", "timelinebase");
__publicField(TimelineBase, "configurable", {
  // Not yet supported
  animateTreeNodeToggle: false,
  partnerSharedConfigs: {
    value: ["timeAxisViewModel", "timeAxis", "viewPreset", "infiniteScroll"],
    $config: {
      merge: "distinct"
    }
  },
  /**
   * Get/set startDate. Defaults to current date if none specified.
   *
   * When using {@link #config-infiniteScroll}, use {@link #config-visibleDate} to control initially visible date
   * instead.
   *
   * **Note:** If you need to set start and end date at the same time, use {@link #function-setTimeSpan} method.
   * @member {Date} startDate
   * @category Common
   */
  /**
   * The start date of the timeline (if not configure with {@link #config-infiniteScroll}).
   *
   * If omitted, and a TimeAxis has been set, the start date of the provided {@link Scheduler.data.TimeAxis} will
   * be used. If no TimeAxis has been configured, it'll use the start/end dates of the loaded event dataset. If no
   * date information exists in the event data set, it defaults to the current date and time.
   *
   * If a string is supplied, it will be parsed using
   * {@link Core/helper/DateHelper#property-defaultFormat-static DateHelper.defaultFormat}.
   *
   * When using {@link #config-infiniteScroll}, use {@link #config-visibleDate} to control initially visible date
   * instead.
   *
   * **Note:** If you need to set start and end date at the same time, use the {@link #function-setTimeSpan} method.
   * @config {Date|String}
   * @category Common
   */
  startDate: {
    $config: {
      equal: "date"
    },
    value: null
  },
  /**
   * Get/set endDate. Defaults to startDate + default span of the used ViewPreset.
   *
   * **Note:** If you need to set start and end date at the same time, use {@link #function-setTimeSpan} method.
   * @member {Date} endDate
   * @category Common
   */
  /**
   * The end date of the timeline (if not configure with {@link #config-infiniteScroll}).
   *
   * If omitted, it will be calculated based on the {@link #config-startDate} setting and the 'defaultSpan'
   * property of the current {@link #config-viewPreset}.
   *
   * If a string is supplied, it will be parsed using
   * {@link Core/helper/DateHelper#property-defaultFormat-static DateHelper.defaultFormat}.
   *
   * **Note:** If you need to set start and end date at the same time, use the {@link #function-setTimeSpan} method.
   * @config {Date|String}
   * @category Common
   */
  endDate: {
    $config: {
      equal: "date"
    },
    value: null
  },
  /**
   * Get/set start date limit of the timeline. Actions such as timeline scrolling, all types of timeline zooms and shifts will respect this limit.
   * @config {Date|String}
   * @category Common
   */
  minDate: null,
  /**
   * Get/set end date limit of the timeline. Actions such as timeline scrolling, all types of timeline zooms and shifts will respect this limit.
   * @config {Date|String}
   * @category Common
   */
  maxDate: null,
  /**
   * Partners this Timeline panel with another Timeline in order to sync their region sizes (sub-grids like locked, normal will get the same width),
   * start and end dates, view preset, zoom level and scrolling position. All these values will be synced with the timeline defined as the `partner`.
   *
   * - To add a new partner dynamically see {@link #function-addPartner} method.
   * - To remove existing partner see {@link #function-removePartner} method.
   * - To check if timelines are partners see {@link #function-isPartneredWith} method.
   *
   * Column widths and hide/show state are synced between partnered schedulers when the column set is identical.
   * @config {Scheduler.view.TimelineBase}
   * @category Time axis
   */
  partner: null,
  /**
   * When set, the text in the major time axis header sticks in the scrolling viewport as long as possible.
   * @config {Boolean}
   * @default
   * @category Time axis
   */
  stickyHeaders: true,
  /**
   * A scrolling `options` object describing the scroll action, including a `date` option
   * which references a `Date`. See {@link #function-scrollToDate} for details about scrolling options.
   *
   * ```javascript
   *     // The date we want in the center of the Scheduler viewport
   *     myScheduler.visibleDate = {
   *         date    : new Date(2023, 5, 17, 12),
   *         block   : 'center',
   *         animate : true
   *     };
   * ```
   * @member {Object} visibleDate
   * @category Common
   */
  /**
   * A date to bring into view initially on the scrollable timeline.
   *
   * This may be configured as either a `Date` or a scrolling `options` object describing
   * the scroll action, including a `date` option which references a `Date`.
   *
   * See {@link #function-scrollToDate} for details about scrolling options.
   *
   * Note that if a naked `Date` is passed, it will be stored internally as a scrolling options object
   * using the following defaults:
   *
   * ```javascript
   * {
   *     date  : <The Date object>,
   *     block : 'nearest'
   * }
   * ```
   *
   * This moves the date into view by the shortest scroll, so that it just appears at an edge.
   *
   * To bring your date of interest to the center of the viewport, configure your
   * Scheduler thus:
   *
   * ```javascript
   *     visibleDate : {
   *         date  : new Date(2023, 5, 17, 12),
   *         block : 'center'
   *     }
   * ```
   * @config {Date|VisibleDate}
   * @category Common
   */
  visibleDate: null,
  /**
   * CSS class to add to rendered events
   * @config {String}
   * @category CSS
   * @private
   */
  eventCls: null,
  /**
   * Set to `true` to force the time columns to fit to the available space (horizontal or vertical depends on mode).
   * Note that setting {@link #config-suppressFit} to `true`, will disable `forceFit` functionality. Zooming
   * cannot be used when `forceFit` is set.
   * @prp {Boolean}
   * @default
   * @category Time axis
   */
  forceFit: false,
  /**
   * Set to a time zone or a UTC offset. This will set the projects
   * {@link Scheduler.model.ProjectModel#config-timeZone} config accordingly. As this config is only a referer,
   * please see project's config {@link Scheduler.model.ProjectModel#config-timeZone documentation} for more
   * information.
   *
   * ```javascript
   * new Calendar(){
   *   timeZone : 'America/Chicago'
   * }
   * ```
   * @prp {String|Number} timeZone
   * @category Misc
   */
  timeZone: null,
  /**
   * By default the row hover effect is not visible in the Scheduler part of the grid.
   *
   * Set this to `false` to show the hover effect in Scheduler rows.
   * @prp {Boolean} hideRowHover
   * @default true
   * @category Misc
   */
  hideRowHover: {
    $config: "lazy",
    value: true
  }
});
TimelineBase.initClass();
VersionHelper.setVersion("scheduler", "6.2.5");
TimelineBase._$name = "TimelineBase";

// ../Scheduler/lib/Scheduler/data/plugin/ResourceStoreLazyLoadPlugin.js
var ResourceStoreLazyLoadPlugin = class extends AjaxStoreLazyLoad {
  internalUnloadRecord({ record }) {
    var _a5;
    const me = this, { client } = me, { eventStore } = client, assignments = [...record.assignments], unloadedAssignments = (_a5 = me.$unloadedAssignments) != null ? _a5 : me.$unloadedAssignments = [], removeUnassignedEvents = !eventStore.usesSingleAssignment && eventStore.removeUnassignedEvent;
    unloadedAssignments.push(...assignments);
    if (removeUnassignedEvents) {
      eventStore.removeUnassignedEvent = false;
    }
    super.internalUnloadRecord({ record });
    me.pauseStm();
    client.afterResourceRemoval();
    me.resumeStm();
    if (removeUnassignedEvents) {
      eventStore.removeUnassignedEvent = true;
    }
    client.assignmentStore.removed.remove(assignments);
    unloadedAssignments.forEach((a) => {
      a.resource = a.resourceId;
      a.event = a.eventId;
    });
  }
  addData(resourcesData) {
    const { $unloadedAssignments, client } = this;
    let refresh = false;
    super.addData(...arguments);
    if ($unloadedAssignments == null ? void 0 : $unloadedAssignments.length) {
      const assignmentsToAdd = $unloadedAssignments.filter((a) => resourcesData.some((r) => r.id === a.resourceId));
      if (assignmentsToAdd.length) {
        this.pauseStm();
        client.assignmentStore.add(assignmentsToAdd);
        this.resumeStm();
        refresh = true;
      }
    }
    refresh && client.trigger("refresh", { action: "lazyload" });
  }
};
__publicField(ResourceStoreLazyLoadPlugin, "$name", "ResourceStoreLazyLoadPlugin");
ResourceStoreLazyLoadPlugin._$name = "ResourceStoreLazyLoadPlugin";

// ../Scheduler/lib/Scheduler/data/mixin/ResourceStoreMixin.js
var ResourceStoreMixin_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    get isResourceStore() {
      return true;
    }
    /**
     * Add resources to the store.
     *
     * NOTE: References (events, assignments) on the resources are determined async by a calculation engine. Thus they
     * cannot be directly accessed after using this function.
     *
     * For example:
     *
     * ```javascript
     * const [resource] = resourceStore.add({ id });
     * // resource.events is not yet available
     * ```
     *
     * To guarantee references are set up, wait for calculations for finish:
     *
     * ```javascript
     * const [resource] = resourceStore.add({ id });
     * await resourceStore.project.commitAsync();
     * // resource.events is available (assuming EventStore is loaded and so on)
     * ```
     *
     * Alternatively use `addAsync()` instead:
     *
     * ```javascript
     * const [resource] = await resourceStore.addAsync({ id });
     * // resource.events is available (assuming EventStore is loaded and so on)
     * ```
     *
     * @param {Scheduler.model.ResourceModel|Scheduler.model.ResourceModel[]|ResourceModelConfig|ResourceModelConfig[]} records
     * Array of records/data or a single record/data to add to store
     * @param {Boolean} [silent] Specify `true` to suppress events
     * @returns {Scheduler.model.ResourceModel[]} Added records
     * @function add
     * @category CRUD
     */
    /**
     * Add resources to the store and triggers calculations directly after. Await this function to have up to date
     * references on the added resources.
     *
     * ```javascript
     * const [resource] = await resourceStore.addAsync({ id });
     * // resource.events is available (assuming EventStore is loaded and so on)
     * ```
     *
     * @param {Scheduler.model.ResourceModel|Scheduler.model.ResourceModel[]|ResourceModelConfig|ResourceModelConfig[]} records
     * Array of records/data or a single record/data to add to store
     * @param {Boolean} [silent] Specify `true` to suppress events
     * @returns {Scheduler.model.ResourceModel[]} Added records
     * @function addAsync
     * @category CRUD
     * @async
     */
    /**
     * Applies a new dataset to the ResourceStore. Use it to plug externally fetched data into the store.
     *
     * NOTE: References (events, assignments) on the resources are determined async by a calculation engine. Thus
     * they cannot be directly accessed after assigning the new dataset.
     *
     * For example:
     *
     * ```javascript
     * resourceStore.data = [{ id }];
     * // resourceStore.first.events is not yet available
     * ```
     *
     * To guarantee references are available, wait for calculations for finish:
     *
     * ```javascript
     * resourceStore.data = [{ id }];
     * await resourceStore.project.commitAsync();
     * // resourceStore.first.events is available
     * ```
     *
     * Alternatively use `loadDataAsync()` instead:
     *
     * ```javascript
     * await resourceStore.loadDataAsync([{ id }]);
     * // resourceStore.first.events is available
     * ```
     *
     * @member {ResourceModelConfig[]} data
     * @category Records
     */
    /**
     * Applies a new dataset to the ResourceStore and triggers calculations directly after. Use it to plug externally
     * fetched data into the store.
     *
     * ```javascript
     * await resourceStore.loadDataAsync([{ id }]);
     * // resourceStore.first.events is available
     * ```
     *
     * @param {ResourceModelConfig[]} data Array of ResourceModel data objects
     * @function loadDataAsync
     * @category CRUD
     * @async
     */
    static get defaultConfig() {
      return {
        /**
         * CrudManager must load stores in the correct order. Lowest first.
         * @private
         */
        loadPriority: 200,
        /**
         * CrudManager must sync stores in the correct order. Lowest first.
         * @private
         */
        syncPriority: 100,
        id: "resources",
        autoTree: true
      };
    }
    get $lazyLoadPluginClass() {
      return ResourceStoreLazyLoadPlugin;
    }
    construct(config) {
      super.construct(config);
      if (!this.modelClass.isResourceModel) {
        throw new Error("Model for ResourceStore must subclass ResourceModel");
      }
    }
    removeAll() {
      const result = super.removeAll(...arguments);
      result && this.assignmentStore.removeAll();
      return result;
    }
    // Apply id changes also to assignments (used to be handled automatically by relations earlier, but engine does not
    // care about ids so needed now)
    // problems:
    // 1. orientation/HorizontalRendering listens to assignment store changes and is trying to refresh view
    // When we update resource id on assignment, listener will be invoked and view will try to refresh. And it will
    // fail, because row is not updated yet. Flag is raised on resource store to make HorizontalRendering to skip
    // refreshing view in this particular case of resource id changing
    onRecordIdChange({ record, oldValue, value: value2 }) {
      super.onRecordIdChange({ record, oldValue, value: value2 });
      if (record.isFieldModified("id")) {
        this.isChangingId = true;
        record.updateAssignmentResourceIds();
        this.isChangingId = false;
      }
    }
    // Cache used by VerticalRendering, reset from there
    get allResourceRecords() {
      return this._allResourceRecords || (this._allResourceRecords = this.getAllDataRecords());
    }
    /**
     * Returns all resources that have no events assigned during the specified time range.
     * @param {Date} startDate Time range start date
     * @param {Date} endDate Time range end date
     * @returns {Scheduler.model.ResourceModel[]} Resources without events
     */
    getAvailableResources({ startDate, endDate }) {
      return this.query((resource) => this.eventStore.isDateRangeAvailable(startDate, endDate, null, resource));
    }
  }, __publicField(_a5, "$name", "ResourceStoreMixin"), _a5;
};

// ../Scheduler/lib/Scheduler/data/mixin/PartOfProject.js
var PartOfProject_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
  }, /**
   * Returns the project this entity belongs to.
   *
   * @member {Scheduler.model.ProjectModel} project
   * @readonly
   * @category Project
   */
  /**
   * Returns the event store of the project this entity belongs to.
   *
   * @member {Scheduler.data.EventStore} eventStore
   * @readonly
   * @category Project
   */
  /**
   * Returns the dependency store of the project this entity belongs to.
   *
   * @member {Scheduler.data.DependencyStore} dependencyStore
   * @readonly
   * @category Project
   */
  /**
   * Returns the assignment store of the project this entity belongs to.
   *
   * @member {Scheduler.data.AssignmentStore} assignmentStore
   * @readonly
   * @category Project
   */
  /**
   * Returns the resource store of the project this entity belongs to.
   *
   * @member {Scheduler.data.ResourceStore} resourceStore
   * @readonly
   * @category Project
   */
  __publicField(_a5, "$name", "PartOfProject"), _a5;
};

// ../Scheduler/lib/Scheduler/model/mixin/ResourceModelMixin.js
var ResourceModelMixin_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    // Flag checked by ResourceStore to make sure it uses a valid subclass
    static get isResourceModel() {
      return true;
    }
    /**
     * Set value for the specified field(s), triggering engine calculations immediately. See
     * {@link Core.data.Model#function-set Model#set()} for arguments.
     *
     * This does not matter much on the resource itself, but is of importance when manipulating its references:
     *
     * ```javascript
     * assignment.set('resourceId', 2);
     * // resource.assignments is not yet up to date
     *
     * await assignment.setAsync('resourceId', 2);
     * // resource.assignments is up to date
     * ```
     *
     * @param {String|Object} field The field to set value for, or an object with multiple values to set in one call
     * @param {*} [value] Value to set
     * @param {Boolean} [silent=false] Set to `true` to not trigger events
     * automatically.
     * @function setAsync
     * @category Editing
     * @async
     */
    //region Fields
    static get fields() {
      return [
        /**
         * Unique identifier
         * @field {String|Number} id
         * @category Common
         */
        /**
         * Get or set resource name
         * @field {String} name
         * @category Common
         */
        { name: "name", type: "string" },
        /**
         * Controls the primary color used for events assigned to this resource. Can be overridden per event using
         * EventModels {@link Scheduler.model.mixin.EventModelMixin#field-eventColor eventColor config}. Also, see
         * Schedulers {@link Scheduler.view.mixin.TimelineEventRendering#config-eventColor eventColor config}.
         *
         * For available standard colors, see
         * {@link Scheduler.model.mixin.EventModelMixin#typedef-EventColor}.
         *
         * **Note**: In a TreeStore, leaf nodes inherits their parent´s eventColor if they lack their own color.
         *
         * @field {EventColor} eventColor
         * @category Styling
         */
        "eventColor",
        /**
         * Controls the style used for events assigned to this resource. Can be overridden per event using
         * EventModels {@link Scheduler/model/mixin/EventModelMixin#field-eventStyle eventStyle config}. See Schedulers
         * {@link Scheduler.view.mixin.TimelineEventRendering#config-eventStyle eventStyle config} for available
         * options.
         * @field {String} eventStyle
         * @category Styling
         */
        "eventStyle",
        /**
         * Fully qualified image URL, used by `ResourceInfoColumn` and vertical modes `ResourceHeader` to display a miniature image
         * for the resource.
         * @field {String} imageUrl
         * @category Styling
         */
        "imageUrl",
        /**
         * Image name relative to {@link Scheduler/view/mixin/SchedulerEventRendering#config-resourceImagePath},
         * used by `ResourceInfoColumn` and vertical modes `ResourceHeader` to display a miniature image
         * for the resource.
         * Set value to `false` to disable image display.
         * @field {String|Boolean} image
         * @category Styling
         */
        "image",
        /**
         * Control how much space to leave between the first event/last event and the resources edge (top/bottom
         * margin within the resource row in horizontal mode, left/right margin within the resource column in
         * vertical mode), in px.
         *
         * It's also possible to set different values for top/left and bottom/right
         * by assigning  an object to `resourceMargin` with `start` (margin top in horizontal mode,
         * margin left in vertical mode) and `end` (margin bottom / margin right) properties:
         * ```javascript
         * scheduler = new Scheduler({
         *     resourceMargin : {
         *         start : 15,
         *         end   : 1
         *     }
         * });
         * ```
         *
         * @field {Number|ResourceMarginConfig} resourceMargin
         * @category Layout
         */
        "resourceMargin",
        /**
         * Margin between stacked event bars for this resource, in px.
         * @field {Number} barMargin
         * @category Layout
         */
        { name: "barMargin", type: "number" },
        /**
         * Base height of this resource, in px. When unset, Schedulers configured rowHeight is used.
         *
         * This value is used in horizontal mode to determine row height. When stacking, it is used as input for
         * calculating the actual row height:
         *
         * ```javascript
         * row.height = (resource.rowHeight - (resourceMargin.start + resourceMargin.end)) * overlap count - barMargin * (overlap count - 1)
         * ```
         *
         * When packing or overlapping, it is used as the actual row height.
         *
         * @field {Number} rowHeight
         * @category Layout
         */
        /**
         * Base width of this resource, in px. If not set, the `columnWidth` specified in
         * the Scheduler's configured {@link Scheduler.view.Scheduler#config-resourceColumns} is used.
         *
         * This value is used in vertical mode to determine column width.
         *
         * @field {Number} columnWidth
         * @category Layout
         */
        /**
         * Specify this to use a resource specific event layout in horizontal mode, see
         * {@link Scheduler.view.mixin.SchedulerEventRendering#config-eventLayout} for options.
         *
         * When unset (the default) Schedulers setting is used.
         *
         * @field {'stack'|'pack'|'mixed'|'none'} eventLayout
         * @category Layout
         */
        "eventLayout",
        /**
         * Set to `false` to prevent overlapping events for this resource
         * @field {Boolean} allowOverlap
         * @category Layout
         */
        { name: "allowOverlap", type: "boolean" },
        /**
         * Set to `false` to not display an avatar for this resource in the {@link Scheduler/column/ResourceInfoColumn}
         * @field {Boolean} showAvatar
         * @category Styling
         */
        { name: "showAvatar", type: "boolean" }
      ];
    }
    //endregion
    //region Id change
    updateAssignmentResourceIds() {
      this.assigned.forEach((assignment) => {
        assignment.resourceId = this.id;
      });
    }
    syncId(value2) {
      super.syncId(value2);
      this.updateAssignmentResourceIds();
    }
    //endregion
    //region Getters
    // Documented in Scheduler.model.ResourceModel, SchedulerPro.model.ResourceModel, Gantt.model.ResourceModel
    get events() {
      return this.assignments.reduce((events, assignment) => {
        if (assignment.event) {
          events.push(assignment.event);
        }
        return events;
      }, []);
    }
    /**
     * Returns all assignments for the resource
     *
     * @property {Scheduler.model.AssignmentModel[]}
     * @category Common
     */
    get assignments() {
      return this.assigned ? [...this.assigned] : [];
    }
    set assignments(assignments) {
      this.assignmentStore.remove(this.assignments);
      assignments.forEach((assignment) => {
        assignment.resource = this;
      });
    }
    /**
     * Returns `true` if the resource can be persisted.
     * In a flat store, a resource is always considered persistable. In a tree store, a resource is considered
     * persistable if its parent node is persistable.
     *
     * @property {Boolean}
     * @readonly
     * @category Editing
     */
    get isPersistable() {
      return super.isPersistable && (!this.parent || this.parent.isPersistable);
    }
    //endregion
    /**
     * Unassigns this Resource from all its Events
     */
    unassignAll() {
      this.assignments && this.assignmentStore.remove(this.assignments);
    }
    /**
     * Returns the initials (first letter of the first & last space-separated word in the name) or an empty string
     * if this resource has no name. You can override this method in a ResourceModel subclass to provide your own implementation
     *
     * @property {String}
     * @readonly
     * @category Common
     */
    get initials() {
      const { name } = this;
      if (!name) {
        return "";
      }
      const names2 = name.trim().split(" "), firstInitial = names2[0][0], lastInitial = names2.length > 1 ? names2[names2.length - 1][0] : "";
      return firstInitial + lastInitial;
    }
    isWorkingTime(date2) {
      var _a6, _b2;
      const calendar = this.effectiveCalendar || ((_a6 = this.project) == null ? void 0 : _a6.calendar);
      return !calendar || ((_b2 = calendar.isWorkingTime) == null ? void 0 : _b2.call(calendar, date2));
    }
    getEventStartingAfter(date2) {
      let earliestStart = Number.MAX_VALUE, eventRecord;
      this.events.forEach((event) => {
        if (event.isScheduled && event.startDate >= date2 && event.startDate < earliestStart) {
          eventRecord = event;
          earliestStart = event.startDate;
        }
      });
      return eventRecord;
    }
    getEventEndingBefore(date2) {
      let latestEnd = 0, eventRecord;
      this.events.forEach((event) => {
        if (event.isScheduled && event.endDate <= date2 && event.endDate > latestEnd) {
          eventRecord = event;
          latestEnd = event.endDate;
        }
      });
      return eventRecord;
    }
    get eventColor() {
      var _a6;
      return this.get("eventColor") || ((_a6 = this.parent) == null ? void 0 : _a6.eventColor);
    }
    set eventColor(color) {
      return this.set("eventColor", color);
    }
  }, __publicField(_a5, "$name", "ResourceModelMixin"), _a5;
};

// ../chronograph/src/collection/Iterator.js
function split(iterable) {
  const gen1Pending = [];
  const gen2Pending = [];
  let iterator;
  const gen1 = function* () {
    if (!iterator)
      iterator = iterable[Symbol.iterator]();
    while (true) {
      if (gen1Pending.length) {
        yield* gen1Pending;
        gen1Pending.length = 0;
      }
      if (!iterator)
        break;
      const { value: value2, done } = iterator.next();
      if (done) {
        iterator = null;
        iterable = null;
        break;
      }
      gen2Pending.push(value2);
      yield value2;
    }
  };
  const gen2 = function* () {
    if (!iterator)
      iterator = iterable[Symbol.iterator]();
    while (true) {
      if (gen2Pending.length) {
        yield* gen2Pending;
        gen2Pending.length = 0;
      }
      if (!iterator)
        break;
      const { value: value2, done } = iterator.next();
      if (done) {
        iterator = null;
        iterable = null;
        break;
      }
      gen1Pending.push(value2);
      yield value2;
    }
  };
  return [gen1(), gen2()];
}
function* inBatchesBySize(iterator, batchSize) {
  if (batchSize < 0)
    throw new Error("Batch size needs to a natural number");
  batchSize = batchSize | 0;
  const runningBatch = [];
  for (const el of iterator) {
    if (runningBatch.length === batchSize) {
      yield runningBatch;
      runningBatch.length = 0;
    }
    runningBatch.push(el);
  }
  if (runningBatch.length > 0)
    yield runningBatch;
}
function* filter(iterator, func) {
  let i = 0;
  for (const el of iterator) {
    if (func(el, i++))
      yield el;
  }
}
function* drop(iterator, howMany) {
  let i = 0;
  for (const el of iterator) {
    if (++i > howMany)
      yield el;
  }
}
function every(iterator, func) {
  let i = 0;
  for (const el of iterator) {
    if (!func(el, i++))
      return false;
  }
  return true;
}
function some(iterator, func) {
  let i = 0;
  for (const el of iterator) {
    if (func(el, i++))
      return true;
  }
  return false;
}
function* map(iterator, func) {
  let i = 0;
  for (const el of iterator)
    yield func(el, i++);
}
function reduce(iterator, func, initialAcc) {
  let i = 0;
  let acc = initialAcc;
  for (const el of iterator) {
    acc = func(acc, el, i++);
  }
  return acc;
}
function* uniqueOnly(iterator) {
  const seen = /* @__PURE__ */ new Set();
  for (const el of iterator) {
    if (!seen.has(el)) {
      seen.add(el);
      yield el;
    }
  }
}
function* uniqueOnlyBy(iterator, func) {
  const seen = /* @__PURE__ */ new Set();
  for (const el of iterator) {
    const uniqueBy = func(el);
    if (!seen.has(uniqueBy)) {
      seen.add(uniqueBy);
      yield el;
    }
  }
}
function* takeWhile(iterator, func) {
  let i = 0;
  for (const el of iterator) {
    if (func(el, i++))
      yield el;
    else
      return;
  }
}
function* concatIterable(iteratorsProducer) {
  for (const iterator of iteratorsProducer)
    yield* iterator;
}
var ChainedIteratorClass = class _ChainedIteratorClass {
  constructor(iterable) {
    this.iterable = void 0;
    if (!iterable)
      throw new Error("Require an iterable instance for chaining");
    this.iterable = iterable;
  }
  derive(iterable) {
    this.iterable = void 0;
    return new _ChainedIteratorClass(iterable);
  }
  copy() {
    const [iter1, iter2] = split(this.iterable);
    this.iterable = iter2;
    return new _ChainedIteratorClass(iter1);
  }
  split() {
    const [iter1, iter2] = split(this.iterable);
    return [new _ChainedIteratorClass(iter1), this.derive(iter2)];
  }
  inBatchesBySize(batchSize) {
    return this.derive(inBatchesBySize(this.iterable, batchSize));
  }
  filter(func) {
    return this.derive(filter(this.iterable, func));
  }
  drop(howMany) {
    return this.derive(drop(this.iterable, howMany));
  }
  map(func) {
    return this.derive(map(this.iterable, func));
  }
  reduce(func, initialAcc) {
    return reduce(this, func, initialAcc);
  }
  concat() {
    return this.derive(concatIterable(this.iterable));
  }
  uniqueOnly() {
    return this.derive(uniqueOnly(this.iterable));
  }
  uniqueOnlyBy(func) {
    return this.derive(uniqueOnlyBy(this.iterable, func));
  }
  every(func) {
    return every(this, func);
  }
  some(func) {
    return some(this, func);
  }
  takeWhile(func) {
    return this.derive(takeWhile(this.iterable, func));
  }
  *[Symbol.iterator]() {
    let iterable = this.iterable;
    if (!iterable)
      throw new Error("Chained iterator already exhausted or used to derive the new one");
    this.iterable = void 0;
    yield* iterable;
    iterable = void 0;
  }
  toArray() {
    return Array.from(this);
  }
  sort(order) {
    return Array.from(this).sort(order);
  }
  toSet() {
    return new Set(this);
  }
  toMap() {
    return new Map(this);
  }
  // toMap<K, V> () : T extends [ K, V ] ? Map<K, V> : never  {
  //     return new Map<K, V>(this.iterable as (T extends [ K, V ] ? Iterable<T> : never)) as (T extends [ K, V ] ? Map<K, V> : never)
  // }
  flush() {
    for (const element of this) {
    }
  }
  memoize() {
    return new MemoizedIteratorClass(this);
  }
};
var ChainedIterator = (iterator) => new ChainedIteratorClass(iterator);
var CI = ChainedIterator;
var MemoizedIteratorClass = class extends ChainedIteratorClass {
  constructor() {
    super(...arguments);
    this.elements = [];
    this.$iterator = void 0;
  }
  set iterable(iterable) {
    this.$iterable = iterable;
  }
  get iterable() {
    return this;
  }
  derive(iterable) {
    return new ChainedIteratorClass(iterable);
  }
  *[Symbol.iterator]() {
    const elements = this.elements;
    if (this.$iterable) {
      if (!this.$iterator)
        this.$iterator = this.$iterable[Symbol.iterator]();
      let iterator = this.$iterator;
      let alreadyConsumed = elements.length;
      if (alreadyConsumed > 0)
        yield* elements;
      while (true) {
        if (elements.length > alreadyConsumed) {
          for (let i = alreadyConsumed; i < elements.length; i++)
            yield elements[i];
          alreadyConsumed = elements.length;
        }
        if (!iterator)
          break;
        const { value: value2, done } = iterator.next();
        if (done) {
          iterator = this.$iterator = null;
          this.$iterable = null;
        } else {
          elements.push(value2);
          alreadyConsumed++;
          yield value2;
        }
      }
    } else {
      yield* elements;
    }
  }
};
var MemoizedIterator = (iterator) => new MemoizedIteratorClass(iterator);
var MI = MemoizedIterator;

// ../chronograph/src/class/Mixin.js
var MixinInstanceOfProperty = Symbol("MixinIdentity");
var MixinStateProperty = Symbol("MixinStateProperty");
var MixinWalkDepthState = class {
  constructor() {
    this.baseEl = void 0;
    this.sourceEl = void 0;
    this.$elementsByTopoLevel = void 0;
    this.$topoLevels = void 0;
    this.linearizedByTopoLevelsSource = MI(this.linearizedByTopoLevels());
  }
  static new(props) {
    const me = new this();
    props && Object.assign(me, props);
    return me;
  }
  get topoLevels() {
    if (this.$topoLevels !== void 0)
      return this.$topoLevels;
    return this.$topoLevels = this.buildTopoLevels();
  }
  buildTopoLevels() {
    return Array.from(this.elementsByTopoLevel.keys()).sort((level1, level2) => level1 - level2);
  }
  get elementsByTopoLevel() {
    if (this.$elementsByTopoLevel !== void 0)
      return this.$elementsByTopoLevel;
    return this.$elementsByTopoLevel = this.buildElementsByTopoLevel();
  }
  getOrCreateLevel(map2, topoLevel) {
    let elementsAtLevel = map2.get(topoLevel);
    if (!elementsAtLevel) {
      elementsAtLevel = [];
      map2.set(topoLevel, elementsAtLevel);
    }
    return elementsAtLevel;
  }
  buildElementsByTopoLevel() {
    let maxTopoLevel = 0;
    const baseElements = this.baseEl ? CI(this.baseEl.walkDepthState.elementsByTopoLevel.values()).concat().toSet() : /* @__PURE__ */ new Set();
    const map2 = CI(this.sourceEl.requirements).map((mixin2) => mixin2.walkDepthState.elementsByTopoLevel).concat().reduce((elementsByTopoLevel, [topoLevel, mixins]) => {
      if (topoLevel > maxTopoLevel)
        maxTopoLevel = topoLevel;
      this.getOrCreateLevel(elementsByTopoLevel, topoLevel).push(mixins);
      return elementsByTopoLevel;
    }, /* @__PURE__ */ new Map());
    this.getOrCreateLevel(map2, maxTopoLevel + 1).push([this.sourceEl]);
    return CI(map2).map(([level, elements]) => {
      return [level, CI(elements).concat().uniqueOnly().filter((mixin2) => !baseElements.has(mixin2)).sort((mixin1, mixin2) => mixin1.id - mixin2.id)];
    }).toMap();
  }
  *linearizedByTopoLevels() {
    yield* CI(this.topoLevels).map((level) => this.elementsByTopoLevel.get(level)).concat();
  }
};
var MIXIN_ID = 1;
var identity = (a) => class extends a {
};
var ZeroBaseClass = class {
};
var MixinState = class {
  constructor() {
    this.id = MIXIN_ID++;
    this.requirements = [];
    this.baseClass = ZeroBaseClass;
    this.identitySymbol = void 0;
    this.mixinLambda = identity;
    this.walkDepthState = void 0;
    this.$minimalClass = void 0;
    this.name = "";
  }
  static new(props) {
    const me = new this();
    props && Object.assign(me, props);
    me.walkDepthState = MixinWalkDepthState.new({ sourceEl: me, baseEl: getMixinState(me.baseClass) });
    const mixinLambda = me.mixinLambda;
    const symbol = me.identitySymbol = Symbol(mixinLambda.name);
    const mixinLambdaWrapper = Object.assign(function(base) {
      const extendedClass = mixinLambda(base);
      extendedClass.prototype[symbol] = true;
      return extendedClass;
    }, {
      [MixinInstanceOfProperty]: symbol,
      [MixinStateProperty]: me
    });
    Object.defineProperty(mixinLambdaWrapper, Symbol.hasInstance, { value: isInstanceOfStatic });
    me.mixinLambda = mixinLambdaWrapper;
    return me;
  }
  get minimalClass() {
    if (this.$minimalClass !== void 0)
      return this.$minimalClass;
    return this.$minimalClass = this.buildMinimalClass();
  }
  // get hash () : MixinHash {
  //     if (this.$hash !== '') return this.$hash
  //
  //     return this.$hash = this.buildHash()
  // }
  // buildHash () : MixinHash {
  //     return String.fromCharCode(...this.walkDepthState.linearizedByTopoLevelsSource.map(mixin => mixin.id))
  // }
  getBaseClassMixinId(baseClass) {
    const constructor = this.constructor;
    const mixinId = constructor.baseClassesIds.get(baseClass);
    if (mixinId !== void 0)
      return mixinId;
    const newId = MIXIN_ID++;
    constructor.baseClassesIds.set(baseClass, newId);
    return newId;
  }
  buildMinimalClass() {
    const self2 = this.constructor;
    let baseCls = this.baseClass;
    const minimalClassConstructor = this.walkDepthState.linearizedByTopoLevelsSource.reduce((acc, mixin2) => {
      const { cls, hash } = acc;
      const nextHash = hash + String.fromCharCode(mixin2.id);
      let wrapperCls = self2.minimalClassesByLinearHash.get(nextHash);
      if (!wrapperCls) {
        wrapperCls = mixin2.mixinLambda(cls);
        mixin2.name = wrapperCls.name;
        self2.minimalClassesByLinearHash.set(nextHash, wrapperCls);
      }
      acc.cls = wrapperCls;
      acc.hash = nextHash;
      return acc;
    }, { cls: baseCls, hash: String.fromCharCode(this.getBaseClassMixinId(baseCls)) }).cls;
    const minimalClass = Object.assign(minimalClassConstructor, {
      [MixinInstanceOfProperty]: this.identitySymbol,
      [MixinStateProperty]: this,
      mix: this.mixinLambda,
      derive: (base) => Mixin([minimalClass, base], (base2) => class extends base2 {
      }),
      $: this,
      toString: this.toString.bind(this)
    });
    Object.defineProperty(minimalClass, Symbol.hasInstance, { value: isInstanceOfStatic });
    return minimalClass;
  }
  toString() {
    return this.walkDepthState.linearizedByTopoLevelsSource.reduce((acc, mixin2) => `${mixin2.name}(${acc})`, this.baseClass.name);
  }
};
MixinState.minimalClassesByLinearHash = /* @__PURE__ */ new Map();
MixinState.baseClassesIds = /* @__PURE__ */ new Map();
var isMixinClass = (func) => {
  return Object.getPrototypeOf(func.prototype).constructor.hasOwnProperty(MixinStateProperty);
};
var getMixinState = (func) => {
  return Object.getPrototypeOf(func.prototype).constructor[MixinStateProperty];
};
var mixin = (required, mixinLambda) => {
  let baseClass;
  if (required.length > 0) {
    const lastRequirement = required[required.length - 1];
    if (!isMixinClass(lastRequirement) && lastRequirement !== ZeroBaseClass)
      baseClass = lastRequirement;
  }
  const requirements = [];
  required.forEach((requirement, index) => {
    const mixinState2 = requirement[MixinStateProperty];
    if (mixinState2 !== void 0) {
      const currentBaseClass = mixinState2.baseClass;
      if (currentBaseClass !== ZeroBaseClass) {
        if (baseClass) {
          if (baseClass !== currentBaseClass) {
            const currentIsSub = currentBaseClass.prototype.isPrototypeOf(baseClass.prototype);
            const currentIsSuper = baseClass.prototype.isPrototypeOf(currentBaseClass.prototype);
            if (!currentIsSub && !currentIsSuper)
              throw new Error("Base class mismatch");
            baseClass = currentIsSuper ? currentBaseClass : baseClass;
          }
        } else
          baseClass = currentBaseClass;
      }
      requirements.push(mixinState2);
    } else {
      if (index !== required.length - 1)
        throw new Error("Base class should be provided as the last element of the requirements array");
    }
  });
  const mixinState = MixinState.new({
    requirements,
    mixinLambda,
    baseClass: baseClass || ZeroBaseClass
  });
  return mixinState.minimalClass;
};
var isInstanceOfStatic = function(instance) {
  return Boolean(instance && instance[this[MixinInstanceOfProperty]]);
};
var isInstanceOf = (instance, func) => {
  return Boolean(instance && instance[func[MixinInstanceOfProperty]]);
};
var Mixin = mixin;

// ../chronograph/src/class/Base.js
var Base2 = class {
  /**
   * This method applies its 1st argument (if any) to the current instance using `Object.assign()`.
   *
   * Supposed to be overridden in the subclasses to customize the instance creation process.
   *
   * @param props
   */
  initialize(props) {
    props && Object.assign(this, props);
  }
  /**
   * This is a type-safe static constructor method, accepting a single argument, with the object, corresponding to the
   * class properties. It will generate a compilation error, if unknown property is provided.
   *
   * For example:
   *
   * ```ts
   * class MyClass extends Base {
   *     prop     : string
   * }
   *
   * const instance : MyClass = MyClass.new({ prop : 'prop', wrong : 11 })
   * ```
   *
   * will produce:
   *
   * ```plaintext
   * TS2345: Argument of type '{ prop: string; wrong: number; }' is not assignable to parameter of type 'Partial<MyClass>'.
   * Object literal may only specify known properties, and 'wrong' does not exist in type 'Partial<MyClass>'
   * ```
   *
   * The only thing this constructor does is create an instance and call the [[initialize]] method on it, forwarding
   * the first argument. The customization of instance is supposed to be performed in that method.
   *
   * @param props
   */
  static new(props) {
    const instance = new this();
    instance.initialize(props);
    return instance;
  }
};

// ../Engine/lib/Engine/quark/AbstractPartOfProjectGenericMixin.js
var AbstractPartOfProjectGenericMixin = class extends Mixin([], (base) => {
  const superProto2 = base.prototype;
  class AbstractPartOfProjectGenericMixin2 extends base {
    // "Raw" suffix here to not interfere with the automatically added `isPartOfProject`
    // accessor for `PartOfProject` mixin, which is defined outside of engine however
    get isPartOfProjectRaw() {
      return true;
    }
    async commitAsync() {
      return this.project.commitAsync();
    }
    set project(project) {
      this.$project = project;
    }
    get project() {
      return this.$project;
    }
    calculateProject() {
      throw new Error("Implement me");
    }
    /**
     * The method to set the [[AbstractProjectMixin|project]] instance, this entity belongs to.
     */
    setProject(project) {
      return this.project = project;
    }
    /**
     * The method to get the [[AbstractProjectMixin|project]] instance, this entity belongs to.
     */
    getProject() {
      const { project } = this;
      if (project)
        return project;
      return this.setProject(this.calculateProject());
    }
    /**
     * Convenience method to get the instance of the assignment store in the [[AbstractProjectMixin|project]] instance, this entity belongs to.
     */
    getAssignmentStore() {
      const project = this.getProject();
      return project == null ? void 0 : project.assignmentStore;
    }
    /**
     * Convenience method to get the instance of the dependency store in the [[AbstractProjectMixin|project]] instance, this entity belongs to.
     */
    getDependencyStore() {
      const project = this.getProject();
      return project == null ? void 0 : project.dependencyStore;
    }
    /**
     * Convenience method to get the instance of the event store in the [[AbstractProjectMixin|project]] instance, this entity belongs to.
     */
    getEventStore() {
      const project = this.getProject();
      return project == null ? void 0 : project.eventStore;
    }
    /**
     * Convenience method to get the instance of the resource store in the [[AbstractProjectMixin|project]] instance, this entity belongs to.
     */
    getResourceStore() {
      const project = this.getProject();
      return project == null ? void 0 : project.resourceStore;
    }
    /**
     * Convenience method to get the instance of the calendar manager store in the [[AbstractProjectMixin|project]] instance, this entity belongs to.
     */
    getCalendarManagerStore() {
      const project = this.getProject();
      return project == null ? void 0 : project.calendarManagerStore;
    }
  }
  return AbstractPartOfProjectGenericMixin2;
}) {
};

// ../Engine/lib/Engine/quark/CorePartOfProjectGenericMixin.js
var CorePartOfProjectGenericMixin = class extends Mixin([AbstractPartOfProjectGenericMixin], (base) => {
  const superProto2 = base.prototype;
  class CorePartOfProjectGenericMixin2 extends base {
    //region Store getters
    get eventStore() {
      var _a5;
      return (_a5 = this.project) == null ? void 0 : _a5.eventStore;
    }
    get resourceStore() {
      var _a5;
      return (_a5 = this.project) == null ? void 0 : _a5.resourceStore;
    }
    get assignmentStore() {
      var _a5;
      return (_a5 = this.project) == null ? void 0 : _a5.assignmentStore;
    }
    get dependencyStore() {
      var _a5;
      return (_a5 = this.project) == null ? void 0 : _a5.dependencyStore;
    }
    get calendarManagerStore() {
      var _a5;
      return (_a5 = this.project) == null ? void 0 : _a5.calendarManagerStore;
    }
    //endregion
    //region Entity getters
    /**
     * Convenience method to get the instance of event by its id.
     */
    getEventById(id2) {
      var _a5;
      return (_a5 = this.eventStore) == null ? void 0 : _a5.getById(id2);
    }
    /**
     * Convenience method to get the instance of dependency by its id.
     */
    getDependencyById(id2) {
      var _a5;
      return (_a5 = this.dependencyStore) == null ? void 0 : _a5.getById(id2);
    }
    /**
     * Convenience method to get the instance of resource by its id.
     */
    getResourceById(id2) {
      var _a5;
      return (_a5 = this.resourceStore) == null ? void 0 : _a5.getById(id2);
    }
    /**
     * Convenience method to get the instance of assignment by its id.
     */
    getAssignmentById(id2) {
      var _a5;
      return (_a5 = this.assignmentStore) == null ? void 0 : _a5.getById(id2);
    }
    /**
     * Convenience method to get the instance of calendar by its id.
     */
    getCalendarById(id2) {
      var _a5;
      return (_a5 = this.calendarManagerStore) == null ? void 0 : _a5.getById(id2);
    }
  }
  return CorePartOfProjectGenericMixin2;
}) {
};

// ../Engine/lib/Engine/quark/store/mixin/AbstractPartOfProjectStoreMixin.js
var AbstractPartOfProjectStoreMixin = class extends Mixin([
  AbstractPartOfProjectGenericMixin,
  Store
], (base) => {
  const superProto2 = base.prototype;
  class AbstractPartOfProjectStoreMixin2 extends base {
    constructor() {
      super(...arguments);
      this.isLoadingData = false;
      this.disableHasLoadedDataToCommitFlag = false;
    }
    //region Async event triggering
    // NOTE: Tested in Scheduler (EventStore.t.js)
    construct(config = {}) {
      config.asyncEvents = {
        add: true,
        remove: true,
        removeAll: true,
        change: true,
        refresh: true,
        replace: true,
        move: true,
        update: true
      };
      return superProto2.construct.call(this, config);
    }
    // Override for event triggering, to allow triggering events before and after some async operation.
    // The "before" events are prefix, the "after" are not.
    trigger(eventName, param) {
      const me = this, { asyncEvents, project } = me, asyncEvent = asyncEvents == null ? void 0 : asyncEvents[eventName], asyncAction = asyncEvent && (asyncEvent === true || asyncEvent[param.action]);
      if (!asyncAction) {
        return superProto2.trigger.call(me, eventName, param);
      }
      superProto2.trigger.call(me, `${eventName}PreCommit`, { ...param });
      if (!project || project.isEngineReady() && !project.isWritingData) {
        superProto2.trigger.call(me, eventName, param);
      } else if (!me.eventsSuspended && project) {
        if (!project.dataReadyDetacher) {
          project.queuedDataReadyEvents = [];
          project.dataReadyDetacher = project.ion({
            dataReady() {
              this.queuedDataReadyEvents.forEach(([superProto3, scope, eventName2, param2]) => {
                superProto3.trigger.call(scope, eventName2, param2);
              });
              project.queuedDataReadyEvents = null;
              project.dataReadyDetacher();
              project.dataReadyDetacher = null;
            },
            once: true
          });
        }
        project.queuedDataReadyEvents.push([superProto2, me, eventName, param]);
      }
      return true;
    }
    //endregion
    calculateProject() {
      return this.project;
    }
    setStoreData(data) {
      var _a5;
      if (this.project && !(this.syncDataOnLoad || this.disableHasLoadedDataToCommitFlag)) {
        this.project.hasLoadedDataToCommit = true;
      }
      this.isLoadingData = true;
      superProto2.setStoreData.call(this, data);
      this.isLoadingData = false;
      (_a5 = this.project) == null ? void 0 : _a5.trigger("storeRefresh", { store: this });
    }
    // Override to postpone auto commits to after project commit, makes sure records are unmodified after commit
    async doAutoCommit() {
      if (this.suspendCount <= 0 && this.project && !this.project.isEngineReady()) {
        await this.project.commitAsync();
      }
      superProto2.doAutoCommit.call(this);
    }
    async addAsync(records, silent) {
      const result = this.add(records, silent);
      await this.project.commitAsync();
      return result;
    }
    async insertAsync(index, records, silent) {
      const result = this.insert(index, records, silent);
      await this.project.commitAsync();
      return result;
    }
    async loadDataAsync(data) {
      this.data = data;
      await this.project.commitAsync();
    }
  }
  AbstractPartOfProjectStoreMixin2.$name = "AbstractPartOfProjectStoreMixin";
  return AbstractPartOfProjectStoreMixin2;
}) {
};

// ../Engine/lib/Engine/quark/model/mixin/AbstractPartOfProjectModelMixin.js
var AbstractPartOfProjectModelMixin = class extends Mixin([AbstractPartOfProjectGenericMixin, Model], (base) => {
  const superProto2 = base.prototype;
  class AbstractPartOfProjectModelMixin2 extends base {
    joinStore(store) {
      let joinedProject = null;
      if (isInstanceOf(store, AbstractPartOfProjectStoreMixin)) {
        const project = store.getProject();
        if (project && !this.getProject()) {
          this.setProject(project);
          joinedProject = project;
        }
      }
      superProto2.joinStore.call(this, store);
      if (joinedProject && !joinedProject.isRepopulatingStores)
        this.joinProject();
    }
    unjoinStore(store, isReplacing = false) {
      var _a5;
      superProto2.unjoinStore.call(this, store, isReplacing);
      const { project } = this;
      const isLeavingProjectStore = isInstanceOf(store, AbstractPartOfProjectStoreMixin) && !store.isFillingFromMaster && project === (store.isChained && store.project ? store.masterStore.project : store.project);
      if (project && !project.isDestroying && !project.isRepopulatingStores && isLeavingProjectStore) {
        (_a5 = store.untrackRecordFieldChanges) == null ? void 0 : _a5.call(store);
        this.leaveProject(isReplacing);
        this.setProject(null);
      }
      if (isLeavingProjectStore)
        this.graph = null;
    }
    /**
     * Template method, which is called when model is joining the project (through joining some store that
     * has already joined the project)
     */
    joinProject() {
    }
    /**
     * Template method, which is called when model is leaving the project (through leaving some store usually)
     */
    leaveProject(isReplacing = false) {
    }
    calculateProject() {
      var _a5;
      const store = (_a5 = this.stores) == null ? void 0 : _a5.find((s) => isInstanceOf(s, AbstractPartOfProjectStoreMixin) && !!s.getProject());
      return store == null ? void 0 : store.getProject();
    }
    async setAsync(fieldName, value2, silent) {
      var _a5;
      const result = this.set(fieldName, value2, silent);
      await ((_a5 = this.project) == null ? void 0 : _a5.commitAsync());
      return result;
    }
    async getAsync(fieldName) {
      var _a5;
      await ((_a5 = this.project) == null ? void 0 : _a5.commitAsync());
      return this.get(fieldName);
    }
    get isStmRestoring() {
      const project = this.getProject();
      return (project == null ? void 0 : project.isRestoringData) || (project == null ? void 0 : project.stm.isRestoring) || false;
    }
  }
  return AbstractPartOfProjectModelMixin2;
}) {
};

// ../Engine/lib/Engine/quark/model/mixin/CorePartOfProjectModelMixin.js
var CorePartOfProjectModelMixin = class extends Mixin([
  AbstractPartOfProjectModelMixin,
  CorePartOfProjectGenericMixin,
  Model
], (base) => {
  const superProto2 = base.prototype;
  class CorePartOfProjectModelMixin2 extends base {
    constructor() {
      super(...arguments);
      this.$isCalculating = false;
      this.$changed = {};
      this.$beforeChange = {};
    }
    get isInActiveTransaction() {
      return true;
    }
    // Invalidate record upon joining project, leads to a buffered commit
    joinProject() {
      !this.isRoot && this.invalidate();
    }
    // Trigger a buffered commit when leaving the project
    leaveProject(isReplacing = false) {
      var _a5;
      this.project.$leaving.add(this);
      superProto2.leaveProject.call(this, isReplacing);
      (_a5 = this.project) == null ? void 0 : _a5.bufferedCommitAsync();
    }
    /**
     * Invalidates this record, queueing it for calculation on project commit.
     */
    invalidate() {
      var _a5;
      !this._preventInvalidation && ((_a5 = this.project) == null ? void 0 : _a5.invalidate(this));
    }
    /**
     * Used to retrieve the proposed (before 'dataReady') or current (after 'dataReady') value for a field.
     * If there is no proposed change, it is functionally equal to a normal `record.get()` call.
     */
    getCurrentOrProposed(fieldName) {
      var _a5;
      if (fieldName in this.$changed && this.$changed[fieldName] !== true) {
        return this.$changed[fieldName];
      }
      return (_a5 = this.get(fieldName)) != null ? _a5 : null;
    }
    /**
     * Determines if the specified field has a value or not, value can be either current or proposed.
     */
    hasCurrentOrProposed(fieldName) {
      return fieldName in this.$changed && this.$changed[fieldName] != true || this.get(fieldName) != null;
    }
    /**
     * Propose changes, to be considered during calculation. Also invalidates the record.
     */
    propose(changes) {
      var _a5;
      if (this.project || ((_a5 = this.recurringTimeSpan) == null ? void 0 : _a5.project)) {
        const keys = Object.keys(changes);
        for (let i = 0; i < keys.length; i++) {
          const key = keys[i];
          this.$changed[key] = changes[key];
        }
        this.invalidate();
      } else {
        this.set(changes);
      }
    }
    /**
     * Similar to propose, but with more options. Mostly used by buckets, since they need data to update early.
     */
    setChanged(field, value2, invalidate = true, setData = false) {
      const me = this;
      me.$changed[field] = value2;
      if (setData) {
        if (!(field in me.$beforeChange)) {
          me.$beforeChange[field] = me.get(field);
        }
        me.setData(field, value2);
      }
      invalidate && me.invalidate();
    }
    /**
     * Hook called before project refresh, override and calculate required changes in subclasses
     */
    calculateInvalidated() {
    }
    /**
     * Called after project refresh, before dataReady. Announce updated data
     */
    finalizeInvalidated(silent = false) {
      const me = this;
      me.$isCalculating = true;
      if (!silent) {
        me.setData(me.$beforeChange);
        me.set(me.$changed);
      } else {
        me.setData(me.$changed);
      }
      me.$changed = {};
      me.$beforeChange = {};
      me.$isCalculating = false;
    }
  }
  return CorePartOfProjectModelMixin2;
}) {
};

// ../Engine/lib/Engine/quark/model/scheduler_core/CoreResourceMixin.js
var CoreResourceMixin = class extends Mixin([CorePartOfProjectModelMixin], (base) => {
  const superProto2 = base.prototype;
  class CoreResourceMixin2 extends base {
    get assigned() {
      var _a5;
      return (_a5 = this.project) == null ? void 0 : _a5.assignmentStore.getResourcesAssignments(this);
    }
    joinProject() {
      var _a5;
      if (this.resourceStore && !this.resourceStore.isLoadingData) {
        (_a5 = this.assignmentStore) == null ? void 0 : _a5.query((a) => a.get("resource") === this.id).forEach((unresolved) => unresolved.setChanged("resource", this));
      }
      superProto2.joinProject.call(this);
    }
    leaveProject(isReplacing = false) {
      var _a5;
      if (this.assigned && !isReplacing && !((_a5 = this.resourceStore) == null ? void 0 : _a5.isLoadingData)) {
        const resourceStore = this.resourceStore;
        this.assigned.forEach((assignment) => resourceStore.assignmentsForRemoval.add(assignment));
      }
      superProto2.leaveProject.call(this);
    }
    applyValue(useProp, key, value2, skipAccessor, field) {
      if ((field == null ? void 0 : field.name) === "id") {
        this.assigned.forEach((assignment) => {
          assignment.set("resourceId", value2);
        });
      }
      superProto2.applyValue.call(this, useProp, key, value2, skipAccessor, field);
    }
  }
  return CoreResourceMixin2;
}) {
};

// ../Scheduler/lib/Scheduler/model/ResourceModel.js
var EngineMixin = CoreResourceMixin;
var ResourceModel = class extends ResourceModelMixin_default(PartOfProject_default(EngineMixin.derive(GridRowModel))) {
};
/**
 * Get associated events
 *
 * @member {Scheduler.model.EventModel[]} events
 * @readonly
 * @category Common
 */
/**
 * Get associated {@link Scheduler/model/ResourceTimeRangeModel resource time ranges}. Only available when using
 * the `ResourceTimeRanges` feature.
 *
 * @member {Scheduler.model.ResourceTimeRangeModel[]} timeRanges
 * @readonly
 * @category Common
 */
__publicField(ResourceModel, "$name", "ResourceModel");
ResourceModel.exposeProperties();
ResourceModel._$name = "ResourceModel";

// ../Engine/lib/Engine/quark/store/mixin/CorePartOfProjectStoreMixin.js
var CorePartOfProjectStoreMixin = class extends Mixin([
  AbstractPartOfProjectStoreMixin,
  CorePartOfProjectGenericMixin,
  Store
], (base) => {
  const superProto2 = base.prototype;
  class CorePartOfProjectStoreMixin2 extends base {
    setProject(project) {
      const result = superProto2.setProject.call(this, project);
      if (project)
        this.joinProject(project);
      return result;
    }
    joinProject(project) {
    }
    onCommitAsync() {
    }
    joinRecordsToProject(project) {
      const fn2 = (record) => {
        record.setProject(project);
        record.joinProject();
      };
      if (this.rootNode) {
        this.rootNode.traverse(fn2);
      } else {
        this.forEach(fn2);
      }
    }
    unjoinRecordsFromProject(project) {
      const fn2 = (record) => {
        record.leaveProject();
        record.setProject(project);
      };
      const rootNode = this.rootNode;
      if (rootNode) {
        rootNode.traverse((node) => {
          if (node !== rootNode)
            fn2(node);
        });
      } else {
        this.forEach(fn2);
      }
    }
  }
  return CorePartOfProjectStoreMixin2;
}) {
};

// ../Engine/lib/Engine/quark/store/AbstractResourceStoreMixin.js
var dataAddRemoveActions2 = {
  splice: 1,
  clear: 1
};
var AbstractResourceStoreMixin = class extends Mixin([AbstractPartOfProjectStoreMixin], (base) => {
  const superProto2 = base.prototype;
  class AbstractResourceStoreMixin2 extends base {
    constructor() {
      super(...arguments);
      this.assignmentsForRemoval = /* @__PURE__ */ new Set();
    }
    // we need `onDataChange` for `syncDataOnLoad` option to work
    onDataChange(event) {
      var _a5;
      const isAddRemove = dataAddRemoveActions2[event.action];
      super.onDataChange(event);
      if (isAddRemove && ((_a5 = event.removed) == null ? void 0 : _a5.length))
        this.afterResourceRemoval();
    }
    // it seems `onDataChange` is not triggered for `remove` with `silent` flag
    remove(records, silent) {
      const res = superProto2.remove.call(this, records, silent);
      this.afterResourceRemoval();
      return res;
    }
    // it seems `onDataChange` is not triggered for `TreeStore#removeAll()`
    removeAll(silent) {
      const res = superProto2.removeAll.call(this, silent);
      this.afterResourceRemoval();
      return res;
    }
    afterResourceRemoval() {
      const assignmentStore = this.getAssignmentStore();
      if (assignmentStore && !assignmentStore.allAssignmentsForRemoval) {
        const assignmentsForRemoval = [...this.assignmentsForRemoval].filter((assignment) => !assignmentStore.assignmentsForRemoval.has(assignment));
        assignmentsForRemoval.length > 0 && assignmentStore.remove(assignmentsForRemoval);
      }
      this.assignmentsForRemoval.clear();
    }
    processRecord(resourceRecord, isDataset = false) {
      const existingRecord = this.getById(resourceRecord.id);
      const isReplacing = existingRecord && existingRecord !== resourceRecord;
      if (isReplacing) {
        for (const assignment of existingRecord.assigned || []) {
          assignment.resource = resourceRecord;
        }
      }
      return resourceRecord;
    }
  }
  return AbstractResourceStoreMixin2;
}) {
};

// ../Engine/lib/Engine/quark/store/CoreResourceStoreMixin.js
var CoreResourceStoreMixin = class extends Mixin([AbstractResourceStoreMixin, CorePartOfProjectStoreMixin], (base) => {
  const superProto2 = base.prototype;
  class CoreResourceStoreMixin2 extends base {
    static get defaultConfig() {
      return {
        modelClass: CoreResourceMixin
      };
    }
    joinProject() {
      var _a5;
      (_a5 = this.assignmentStore) == null ? void 0 : _a5.linkAssignments(this, "resource");
    }
    afterLoadData() {
      var _a5, _b2;
      (_a5 = superProto2.afterLoadData) == null ? void 0 : _a5.call(this);
      (_b2 = this.assignmentStore) == null ? void 0 : _b2.linkAssignments(this, "resource");
    }
    clear(removing) {
      var _a5;
      superProto2.clear.call(this, removing);
      (_a5 = this.assignmentStore) == null ? void 0 : _a5.unlinkAssignments("resource");
    }
  }
  return CoreResourceStoreMixin2;
}) {
};

// ../Scheduler/lib/Scheduler/data/mixin/PartOfBaseProject.js
var PartOfBaseProject_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    get assignmentStore() {
      return this.project.assignmentStore;
    }
    get calendarManagerStore() {
      return this.project.calendarManagerStore;
    }
    get dependencyStore() {
      return this.project.dependencyStore;
    }
    get eventStore() {
      return this.project.eventStore;
    }
    get resourceStore() {
      return this.project.resourceStore;
    }
  }, __publicField(_a5, "$name", "PartOfBaseProject"), _a5;
};

// ../Scheduler/lib/Scheduler/data/ResourceStore.js
var EngineMixin2 = PartOfProject_default(CoreResourceStoreMixin.derive(AjaxStore));
var ResourceStore = class extends ResourceStoreMixin_default(EngineMixin2) {
  static get defaultConfig() {
    return {
      modelClass: ResourceModel
    };
  }
};
__publicField(ResourceStore, "$name", "ResourceStore");
ResourceStore._$name = "ResourceStore";

// ../Scheduler/lib/Scheduler/data/plugin/DateStoreLazyLoadPlugin.js
var DateRanges = class {
  constructor() {
    __publicField(this, "ranges", []);
    __publicField(this, "promises", []);
  }
  addPromise(promise) {
    if (promise == null ? void 0 : promise.then) {
      this.promises.push(promise);
      promise.then(() => {
        this.promises.splice(this.promises.indexOf(promise), 1);
      });
    }
  }
  addRange(startDate, endDate, promise) {
    this.addPromise(promise);
    const intersectStart = this.getRange(startDate);
    if ((intersectStart == null ? void 0 : intersectStart.endDate) < endDate) {
      intersectStart.endDate.setTime(endDate.getTime());
      return;
    }
    const intersectEnd = this.getRange(endDate);
    if ((intersectEnd == null ? void 0 : intersectEnd.startDate) > startDate) {
      intersectEnd.startDate.setTime(startDate.getTime());
      return;
    }
    startDate = new Date(startDate.getTime());
    endDate = new Date(endDate.getTime());
    this.ranges.push({ startDate, endDate });
  }
  containsRange(startDate, endDate) {
    return this.ranges.some((r) => DateHelper.timeSpanContains(r.startDate, r.endDate, startDate, endDate));
  }
  getRange(date2) {
    return this.ranges.filter((r) => DateHelper.betweenLesserEqual(date2, r.startDate, r.endDate))[0];
  }
  removeRange(startDate, endDate) {
    const rangesToRemove = [], rangesToAdd = [], { ranges } = this;
    ranges.forEach((range, index) => {
      if (DateHelper.intersectSpans(range.startDate, range.endDate, startDate, endDate)) {
        rangesToRemove.push(index);
        if (range.startDate < startDate) {
          rangesToAdd.push([range.startDate, startDate]);
        }
        if (range.endDate > endDate) {
          rangesToAdd.push([endDate, range.endDate]);
        }
      }
    });
    for (let i = rangesToRemove.length - 1; i >= 0; i--) {
      ranges.splice(rangesToRemove[i], 1);
    }
    rangesToAdd.forEach((range) => this.addRange(...range));
  }
};
var DateStoreLazyLoadPlugin = class extends AjaxStoreLazyLoad.mixin(Delayable_default) {
  // region Configs
  static get pluginConfig() {
    var _a5;
    const config = {};
    for (const prop in super.pluginConfig) {
      config[prop] = [...super.pluginConfig[prop]];
    }
    config.assign.push("updateProject");
    const lazyGetAt = config.assign.indexOf("lazyGetAt");
    if (lazyGetAt >= 0) {
      config.assign.splice(lazyGetAt, 1);
    }
    config.before = (_a5 = config.before) != null ? _a5 : [];
    config.before.push("getEventsAsArray");
    return config;
  }
  /**
   * In an EventStore or ResourceTimeRangeStore which is configured with
   * {@link Core.data.Store#config-lazyLoad}, the function provided here is called when a combination of the visible
   * date range and the visible range of resources has not yet been loaded. If the ResourceStore is not configured
   * with {@link Core.data.Store#config-lazyLoad}, the resource range will include all the loaded resources. When
   * implementing this, it is expected that what is returned is an object with a `data` property containing the
   * records from `startDate` to `endDate` for a range of resources starting at `startIndex` and with the length
   * specified in the `count` param.
   *
   * Base implementation does nothing, either use AjaxStore which implements it, or create your own subclass with an
   * implementation.
   *
   * ```javascript
   * class MyEventStore extends EventStore {
   *    async requestData(params){
   *       const response = await fetch('https://api.bryntum.com/events/?' + new URLSearchParams(params));
   *       return await response.json();
   *    }
   * }
   * ```
   *
   * @function requestData
   * @param {Object} options
   * @param {Date} options.startDate The start date of the current timespan
   * @param {Date} options.endDate The end date of the current timespan
   * @param {Number} options.startIndex The resource start index
   * @param {Number} options.count The resource count
   * @returns {Promise}
   * @on-owner
   */
  /**
   * If set to `true`, or a config object, this makes the store load new records when needed. When a record that
   * is not already loaded is requested, the {@link #function-requestData} function is called. Please read the
   * [guide](#Grid/guides/data/lazyloading.md) to learn more on how to configure lazy loading.
   * @param {DurationUnit} bufferUnit Used together with bufferAmount to calculate the start and end dates of each load
   * request. The value is added to the current visible start or end date. Defaults to the visible time span length.
   * @param {Number} bufferAmount See `bufferUnit`
   * @param {String} dateFormat The format used to convert `startDate` and `endDate` parameters passed to the
   * load request querystring. Defaults to `YYYY-MM-DD` (e.g. **2024-03-11**). Only used if a
   * {@link Core.data.AjaxStore#config-readUrl} is configured on the store. See {@link Core.helper.DateHelper} for
   * details about formatting dates.
   * @param {Boolean} loadFullResourceRange If the ResourceStore is not lazy loaded, set this to `true` to load
   * events, assignments and/or resource time ranges for all resources with every load request.
   * @param {Boolean} useResourceIds Set to `true` to always provide `resourceIds` in the request params, instead of
   * a resource `startIndex` and `count`
   * @prp {Boolean|Object} lazyLoad
   * @on-owner
   */
  // endregion
  afterConstruct() {
    super.afterConstruct(...arguments);
    this.listenToProject();
  }
  // region Store overrides and more
  get project() {
    return this.client.project;
  }
  get resourceStore() {
    var _a5;
    return (_a5 = this.project) == null ? void 0 : _a5.resourceStore;
  }
  // Need to know when resource store is set or changed, to add some listeners
  updateProject() {
    this.listenToProject();
  }
  listenToProject() {
    var _a5;
    const { project } = this;
    (_a5 = this.$projectListenersDetacher) == null ? void 0 : _a5.call(this);
    if (project == null ? void 0 : project.ion) {
      this.$projectListenersDetacher = project.ion({
        resourceStoreChange: "listenToResourceStore",
        thisObj: this
      });
    }
    this.listenToResourceStore();
  }
  listenToResourceStore() {
    var _a5;
    const { resourceStore } = this;
    if ((resourceStore == null ? void 0 : resourceStore.lazyLoad) && resourceStore.isResourceStore && !resourceStore.lazyLoad.$isCrudManager) {
      (_a5 = this.$resourceStoreListenersDetacher) == null ? void 0 : _a5.call(this);
      this.$resourceStoreListenersDetacher = resourceStore.ion({
        afterLazyLoad: "afterResourceStoreLazyLoad",
        thisObj: this
      });
    }
  }
  afterResourceStoreLazyLoad(params) {
    const me = this, { resourceStore } = me;
    if (!me.client.isAssignmentStore && (params.isReload || me.$delayedReloadLoad)) {
      if (resourceStore.tree && resourceStore.isLoading) {
        me.$delayedReloadLoad = true;
        return;
      }
      me.load();
      me.$delayedReloadLoad = false;
    }
  }
  // Only affects stores that mixes GetEventsMixin
  getEventsAsArray({ visibleDateRange, startDate, endDate, fromRowHeight, resourceRecord }) {
    if (!fromRowHeight && visibleDateRange) {
      this.bufferedLazyLoad({
        startDate,
        endDate,
        visibleStart: visibleDateRange.startDate,
        visibleEnd: visibleDateRange.endDate,
        resourceRecord
      });
    }
  }
  // Checks if there is any unresolved loading promises
  get isLoading() {
    return super.isLoading || Object.values(this.loadQueue).some((value2) => value2.promises.length);
  }
  // In StoreLazyLoadPlugin this throws if store is loading
  // In a "DateStore" indexes don't matter, so no need for not allowing adds/remove while loading
  internalOnBeforeAdd() {
  }
  // In StoreLazyLoadPlugin this applies a load lock on uncommitted added/removed
  // Don't care about this here
  internalOnChange() {
  }
  // Same as above
  internalOnCommit() {
  }
  updateAutoLoad() {
    super.autoLoad = false;
  }
  // endregion
  // region Range calculation
  calculateDateRange({ startDate, endDate, visibleStart, visibleEnd }, resourceIndex) {
    let resourceQueue = this.getResourceQueue(resourceIndex);
    if (visibleStart && visibleEnd && !this.loadFullDateRange) {
      const {
        bufferAmount,
        bufferUnit,
        $bufferedResourceRecords,
        resourceStore
      } = this, bufferMs = bufferUnit && bufferAmount != null ? DateHelper.asMilliseconds(bufferAmount, bufferUnit) : visibleEnd - visibleStart, calcDate = (date2, ms) => {
        const newDate = DateHelper.clearTime(new Date(date2.getTime() + ms));
        if (newDate < startDate) {
          newDate.setTime(startDate.getTime());
        } else if (newDate > endDate) {
          newDate.setTime(endDate.getTime());
        }
        return newDate;
      }, thresholdStart = calcDate(visibleStart, bufferMs / -2), thresholdEnd = calcDate(visibleEnd, bufferMs / 2);
      if (thresholdStart > thresholdEnd) {
        return false;
      }
      if (resourceQueue == null ? void 0 : resourceQueue.containsRange(thresholdStart, thresholdEnd)) {
        let foundNotLoaded = false;
        if (($bufferedResourceRecords == null ? void 0 : $bufferedResourceRecords.length) > 1) {
          for (let i = 1; i < $bufferedResourceRecords.length; i++) {
            resourceIndex = resourceStore.indexOf($bufferedResourceRecords[i]);
            resourceQueue = this.getResourceQueue(resourceIndex);
            if (!(resourceQueue == null ? void 0 : resourceQueue.containsRange(thresholdStart, thresholdEnd))) {
              foundNotLoaded = true;
              break;
            }
          }
        }
        if (!foundNotLoaded) {
          return false;
        }
      }
      startDate = calcDate(visibleStart, bufferMs * -1);
      endDate = calcDate(visibleEnd, bufferMs);
    } else if (startDate > endDate) {
      return false;
    } else {
      startDate = new Date(startDate.getTime());
      endDate = new Date(endDate.getTime());
    }
    if (resourceQueue) {
      const intersectStart = resourceQueue.getRange(startDate), intersectEnd = resourceQueue.getRange(endDate);
      intersectStart && startDate.setTime(intersectStart.endDate.getTime());
      intersectEnd && endDate.setTime(intersectEnd.startDate.getTime());
    }
    if (startDate - endDate >= 0) {
      return false;
    }
    return { startDate, endDate, resourceIndex };
  }
  calculateResourceRange({ resourceIndex, startDate, endDate }) {
    var _a5, _b2, _c2, _d;
    const { resourceStore, $bufferedResourceRecords } = this;
    let startIndex = 0, endIndex = resourceStore.count - 1;
    if (resourceStore.lazyLoad || !this.loadFullResourceRange && resourceStore.count) {
      const chunkSize = (_b2 = (_a5 = resourceStore.lazyLoad) == null ? void 0 : _a5.chunkSize) != null ? _b2 : this.chunkSize;
      startIndex = resourceIndex - chunkSize;
      endIndex = resourceIndex + chunkSize;
      if (($bufferedResourceRecords == null ? void 0 : $bufferedResourceRecords.length) > chunkSize) {
        const bottomIndex = resourceStore.indexOf($bufferedResourceRecords.pop());
        if (bottomIndex > endIndex) {
          endIndex = bottomIndex + chunkSize;
        }
      }
      startIndex = Math.max(startIndex, 0);
      while (!resourceStore.records[startIndex] && startIndex < endIndex) {
        startIndex += 1;
      }
      while (!resourceStore.records[endIndex] && startIndex < endIndex) {
        endIndex -= 1;
      }
    }
    while (startIndex < resourceIndex && ((_c2 = this.getResourceQueue(startIndex)) == null ? void 0 : _c2.containsRange(startDate, endDate))) {
      startIndex += 1;
    }
    while (endIndex > resourceIndex && ((_d = this.getResourceQueue(endIndex)) == null ? void 0 : _d.containsRange(startDate, endDate))) {
      endIndex -= 1;
    }
    return { startIndex, count: endIndex - startIndex + 1 };
  }
  // endregion
  // region LoadQueue
  getResourceQueue(resourceIndex) {
    var _a5;
    if (resourceIndex != null) {
      const { resourceStore } = this;
      if (!this.client.isTimeRangeStore && resourceStore.isTree) {
        resourceIndex = (_a5 = resourceStore.records[resourceIndex]) == null ? void 0 : _a5.id;
      }
      return this.loadQueue[resourceIndex];
    }
  }
  addToQueue({ startDate, endDate, startIndex = 0, count = 1, promise, resourceIds }) {
    const { loadQueue } = this, add = (i) => (loadQueue[i] || (loadQueue[i] = new DateRanges())).addRange(startDate, endDate, promise);
    if (resourceIds) {
      resourceIds.forEach(add);
    } else {
      for (let i = startIndex; i < startIndex + count; i++) {
        add(i);
      }
    }
  }
  // endregion
  // region Loading data
  // Executes lazyLoad after a certain time/event
  bufferedLazyLoad(params) {
    const me = this, { resourceRecord, ...dates } = params;
    if (!resourceRecord) {
      me.doLazyLoad(...arguments);
    }
    if (me.$bufferedResourceRecords) {
      if (!ObjectHelper.isEqual(me.$lastBufferedParams, dates)) {
        me.requestAnimationFrame(() => {
          me.bufferedLazyLoad(params);
        });
        return;
      }
      me.$bufferedResourceRecords.push(resourceRecord);
    } else {
      me.$bufferedResourceRecords = [resourceRecord];
      me.$lastBufferedParams = dates;
      me.requestAnimationFrame(() => {
        me.doLazyLoad(params);
        me.$bufferedResourceRecords = null;
        me.$lastBufferedParams = null;
      });
    }
  }
  async doLazyLoad({ isReload, ...params }) {
    var _a5, _b2, _c2, _d, _e, _f, _g;
    const me = this, { client, dateFormat } = me, { resourceStore } = me.project, resourceTree = !client.isTimeRangeStore && resourceStore.isTree, resourceIndex = (_a5 = params.resourceIndex) != null ? _a5 : params.resourceRecord ? resourceStore.indexOf(params.resourceRecord) : 0;
    isReload && me.clear();
    params = me.calculateDateRange(params, resourceIndex);
    if (!client.requestData || params === false || resourceTree && (resourceStore.isLoading || !resourceStore.count) || params.resourceIndex < 0) {
      !resourceStore.count && client.clear();
      return;
    }
    if (!client.isTimeRangeStore) {
      const range = me.calculateResourceRange(params);
      if (resourceTree || me.useResourceIds) {
        const resources = resourceStore.getRange(range.startIndex, range.startIndex + range.count + 1), toLoad = resources.filter((r) => {
          var _a6;
          return !r.isPhantom && !((_a6 = me.loadQueue[r.id]) == null ? void 0 : _a6.containsRange(params.startDate, params.endDate));
        });
        if (!toLoad.length) {
          return;
        }
        params.resourceIds = toLoad.map((r) => r.id);
      } else {
        Object.assign(params, range);
        if (resourceStore.remoteFilter && resourceStore.isFiltered) {
          if (client.readUrl) {
            let { filterParamName = "filters" } = resourceStore;
            if (client.filterParamName === filterParamName) {
              filterParamName = "resource" + filterParamName;
            }
            params[filterParamName] = resourceStore.encodeFilterParams(resourceStore.filters);
          } else {
            params.resourceFilters = resourceStore.getFilterParams(resourceStore.filters);
          }
        }
        if (resourceStore.remoteSort && resourceStore.isSorted) {
          const { sorters } = resourceStore;
          if (client.readUrl) {
            let { sortParamName } = resourceStore;
            if (client.sortParamName === sortParamName) {
              sortParamName = "resource" + sortParamName;
            }
            params[sortParamName] = resourceStore.encodeSorterParams(sorters);
          } else {
            params.resourceFilters = sorters;
          }
        }
      }
    }
    delete params.resourceIndex;
    me.triggerLazyLoadStart(params);
    let requestParams = params;
    if (client.readUrl && dateFormat) {
      requestParams = {
        ...params,
        startDate: DateHelper.format(params.startDate, dateFormat),
        endDate: DateHelper.format(params.endDate, dateFormat)
      };
    } else if (!client.readUrl) {
      client.buildRemoteParams(requestParams);
    }
    const promise = client.requestData(requestParams);
    me.addToQueue({ ...params, promise });
    if (client.isEventStore && !((_d = (_c2 = (_b2 = client.crudManager) == null ? void 0 : _b2.transport) == null ? void 0 : _c2.load) == null ? void 0 : _d.url)) {
      await me.loadAssignmentStore(params, isReload);
      (_g = (_f = (_e = me.project) == null ? void 0 : _e.timeRangeStore) == null ? void 0 : _f.lazyLoad) == null ? void 0 : _g.doLazyLoad(params);
    }
    const { data } = await promise;
    me.addData(data, params, isReload);
    me.triggerLazyLoadEnd(params, data);
  }
  addData(data, { resourceIds, startDate, endDate } = {}, isReload) {
    const { client } = this;
    if ((data == null ? void 0 : data.length) && resourceIds) {
      const additionalResources = data.filter((e) => !resourceIds.includes(e.resourceId));
      if (additionalResources.length) {
        this.addToQueue({ startDate, endDate, resourceIds: additionalResources.map((d) => d.resourceId) });
      }
    }
    if (client.isTree) {
      client.rootNode.appendChild(data, true);
      client.added.clear();
      return;
    }
    super.addData(data, null, isReload);
  }
  processAddedRecords(records) {
    var _a5, _b2;
    const { client } = this;
    if (client.isEventStore && client.$processResourceIds) {
      const assignmentStore = (_b2 = client.assignmentStore) != null ? _b2 : (_a5 = client.crudManager) == null ? void 0 : _a5.assignmentStore, assignments = [];
      for (const {
        resourceId,
        resourceIds,
        id: id2
      } of records) {
        if (resourceId != null) {
          assignments.push({
            resource: resourceId,
            eventId: id2
          });
        } else if (resourceIds == null ? void 0 : resourceIds.length) {
          assignments.push(...resourceIds.map((resourceId2) => ({
            resource: resourceId2,
            eventId: id2
          })));
        }
      }
      this.pauseStm();
      client.project.hasLoadedDataToCommit = true;
      assignmentStore.add(assignments);
      assignmentStore.acceptChanges();
      this.resumeStm();
      client.$processResourceIds = false;
    }
  }
  async loadAssignmentStore(params, isReload) {
    const { project } = this, { assignmentStore } = project, { lazyLoad } = assignmentStore;
    if (lazyLoad) {
      lazyLoad.triggerLazyLoadStart();
      isReload && lazyLoad.clear();
      const promise = assignmentStore.requestData(params);
      lazyLoad.addToQueue({ ...params, promise });
      const { data } = await promise;
      project.hasLoadedDataToCommit = true;
      lazyLoad.addData(data, void 0, isReload);
      lazyLoad.triggerLazyLoadEnd();
    }
  }
  load(params = {}) {
    var _a5;
    (_a5 = this.beforeLoadCall) == null ? void 0 : _a5.call(this, params);
    return this.doLazyLoad({ isReload: true, ...params });
  }
  // endregion
  /**
   * Only available if the store is configured as {@link Core.data.Store#config-lazyLoad}.
   *
   * The records matching the provided parameters will be removed from the Store and reloaded the next they are
   * needed.
   *
   * Provide an array of records or record ids, or provide a combination of resource records (or ids) and/or a date
   * range. Do not combine the `records` params with the `resources` and `startDate` and `endDate` params.
   * @param {Object} options
   * @param {Core.data.Model|Number|String|Core.data.Model[]|Number[]|String[]} [options.records] The records to unload. Can
   *        be a record instance, record id, or an array of record instances or ids.
   * @param {Scheduler.model.ResourceModel|Number|String|Scheduler.model.ResourceModel[]|Number[]|String[]} [options.resources]
   *        The resources to unload records for. Requires the `startDate` and `endDate` params.
   * @param {Date} [options.startDate] The start date of the range to unload records for. Requires the `endDate` param
   *        and also the `resources` param (except for the TimeRangeStore).
   * @param {Date} [options.endDate] The end date of the range to unload records for. Requires the `startDate` param
   *        and also the `resources` param (except for the TimeRangeStore).
   * @typings options.includeDescendants -> {Boolean}
   * @on-owner
   */
  unload({ records, resources, startDate, endDate }) {
    var _a5;
    const me = this, { client } = me, {
      isTimeRangeStore,
      assignmentStore
    } = client;
    if (me.$isCrudManager) {
      console.warn("It is not supported to unload records from the Store when using a lazy loaded CrudManager");
      return;
    }
    me.pauseStm();
    if (!ObjectHelper.isObject(arguments[0])) {
      records = arguments[0];
    }
    if (records) {
      records = ArrayHelper.asArray(records);
      records.forEach((record) => {
        var _a6, _b2;
        if (isTimeRangeStore) {
          (_a6 = me.getResourceQueue(0)) == null ? void 0 : _a6.removeRange(record.startDate, record.endDate);
          startDate = !startDate || record.startDate < startDate ? record.startDate : startDate;
          endDate = record.endDate > endDate ? record.endDate : endDate;
        } else {
          const resourceIds = (_b2 = record.resourceIds) != null ? _b2 : [record.resourceId];
          resourceIds.forEach((resourceId) => {
            const resourceQueue = me.getResourceQueue(client.resourceStore.indexOf(resourceId));
            resourceQueue == null ? void 0 : resourceQueue.removeRange(record.startDate, record.endDate);
          });
        }
      });
    } else if ((resources || isTimeRangeStore) && startDate && endDate) {
      if (isTimeRangeStore) {
        (_a5 = me.getResourceQueue(0)) == null ? void 0 : _a5.removeRange(startDate, endDate);
      } else {
        resources = ArrayHelper.asArray(resources);
        resources.forEach((resource) => {
          const resourceQueue = me.getResourceQueue(client.resourceStore.indexOf(resource));
          resourceQueue == null ? void 0 : resourceQueue.removeRange(startDate, endDate);
        });
      }
      records = client.records.filter((record) => {
        if (resources) {
          if (!resources.some((resource) => {
            var _a6;
            const id2 = resource.isModel ? resource.id : resource;
            return !(!((_a6 = record.resourceIds) == null ? void 0 : _a6.includes(id2)) && record.resourceId !== id2);
          })) {
            return false;
          }
        }
        if (startDate) {
          return DateHelper.intersectSpans(record.startDate, record.endDate, startDate, endDate);
        }
        return true;
      });
    } else {
      return;
    }
    client.remove(records, true);
    client.removed.remove(records);
    me.resumeStm();
    if (client.isEventStore && assignmentStore.lazyLoad) {
      assignmentStore.unload({ records: assignmentStore.records.filter((r) => records.includes(r.event)) });
    }
    if (isTimeRangeStore) {
      me.doLazyLoad({ startDate, endDate });
    }
    client.trigger("refresh", { action: "lazyload" });
  }
  resumeStm(immediate) {
    if (!immediate) {
      super.resumeStm(new Promise((resolve) => {
        this.client.project.ion({ dataReady: resolve, once: true });
      }));
    }
    super.resumeStm();
  }
  doDestroy() {
    var _a5, _b2;
    (_a5 = this.$projectListenersDetacher) == null ? void 0 : _a5.call(this);
    (_b2 = this.$resourceStoreListenersDetacher) == null ? void 0 : _b2.call(this);
    super.doDestroy();
  }
};
__publicField(DateStoreLazyLoadPlugin, "$name", "DateStoreLazyLoadPlugin");
__publicField(DateStoreLazyLoadPlugin, "configurable", {
  bufferUnit: null,
  bufferAmount: null,
  dateFormat: "YYYY-MM-DD",
  loadFullDateRange: false,
  loadFullResourceRange: false,
  useResourceIds: false
});
DateStoreLazyLoadPlugin._$name = "DateStoreLazyLoadPlugin";

// ../Scheduler/lib/Scheduler/data/plugin/PagedDateStoreLazyLoadPlugin.js
var PagedDateStoreLazyLoadPlugin = class extends DateStoreLazyLoadPlugin {
  listenToResourceStore() {
    var _a5, _b2;
    super.listenToResourceStore();
    const me = this, { resourceStore, client } = me;
    if (resourceStore && !((_a5 = client.lazyLoad) == null ? void 0 : _a5.$isCrudManager) && !client.isTimeRangeStore) {
      (_b2 = me.$pagedResourceStoreListenersDetacher) == null ? void 0 : _b2.call(me);
      me.$pagedResourceStoreListenersDetacher = me.resourceStore.ion({
        beforeLoadPage: "beforeResourceStoreLoad",
        thisObj: me
      });
      if (resourceStore.isLoading || resourceStore.count) {
        me.requestAnimationFrame(me.beforeResourceStoreLoad);
      }
    }
  }
  beforeResourceStoreLoad(event) {
    var _a5, _b2;
    const params = { ...(_a5 = event == null ? void 0 : event.params) != null ? _a5 : {}, isReload: true };
    (_b2 = this.beforeLoadCall) == null ? void 0 : _b2.call(this, params);
    this.doLazyLoad(params);
  }
  // Override to keep page and pageSize properties
  calculateDateRange(params) {
    var _a5, _b2, _c2;
    const range = super.calculateDateRange(...arguments);
    if (range) {
      const {
        defaultPageParamName,
        pageSizeParamName
      } = this.client, { resourceStore } = this;
      range[defaultPageParamName] = (_b2 = (_a5 = params[defaultPageParamName]) != null ? _a5 : resourceStore.currentPage) != null ? _b2 : 1;
      range[pageSizeParamName] = (_c2 = params[pageSizeParamName]) != null ? _c2 : resourceStore.pageSize;
    }
    return range;
  }
  // Override to do nothing
  calculateResourceRange(params) {
    return params;
  }
  addToQueue(params) {
    params.startIndex = 0;
    params.count = params.pageSize;
    super.addToQueue(params);
  }
  doDestroy() {
    var _a5;
    super.doDestroy();
    (_a5 = this.$pagedResourceStoreListenersDetacher) == null ? void 0 : _a5.call(this);
  }
};
__publicField(PagedDateStoreLazyLoadPlugin, "$name", "PagedDateStoreLazyLoadPlugin");
__publicField(PagedDateStoreLazyLoadPlugin, "configurable", {
  loadFullDateRange: true
});
PagedDateStoreLazyLoadPlugin._$name = "PagedDateStoreLazyLoadPlugin";

// ../Scheduler/lib/Scheduler/data/mixin/EventStoreMixin.js
var EventStoreMixin_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    get $lazyLoadPluginClass() {
      return this.isPaged ? PagedDateStoreLazyLoadPlugin : DateStoreLazyLoadPlugin;
    }
    /**
     * Add events to the store.
     *
     * NOTE: Dates, durations and references (assignments, resources) on the events are determined async by a calculation
     * engine. Thus they cannot be directly accessed after using this function.
     *
     * For example:
     *
     * ```javascript
     * eventStore.add({ startDate, duration });
     * // endDate is not yet calculated
     * ```
     *
     * To guarantee data is in a calculated state, wait for calculations for finish:
     *
     * ```javascript
     * eventStore.add({ startDate, duration });
     * await eventStore.project.commitAsync();
     * // endDate is calculated
     * ```
     *
     * Alternatively use `addAsync()` instead:
     *
     * ```javascript
     * await eventStore.addAsync({ startDate, duration });
     * // endDate is calculated
     * ```
     *
     * @param {Scheduler.model.EventModel|Scheduler.model.EventModel[]|EventModelConfig|EventModelConfig[]} records
     * Array of records/data or a single record/data to add to store
     * @param {Boolean} [silent] Specify `true` to suppress events
     * @returns {Scheduler.model.EventModel[]} Added records
     * @function add
     * @category CRUD
     */
    /**
     * Add events to the store and triggers calculations directly after. Await this function to have up to date data on
     * the added events.
     *
     * ```javascript
     * await eventStore.addAsync({ startDate, duration });
     * // endDate is calculated
     * ```
     *
     * @param {Scheduler.model.EventModel|Scheduler.model.EventModel[]|EventModelConfig|EventModelConfig[]} records
     * Array of records/data or a single record/data to add to store
     * @param {Boolean} [silent] Specify `true` to suppress events
     * @returns {Scheduler.model.EventModel[]} Added records
     * @function addAsync
     * @category CRUD
     * @async
     */
    /**
     * Applies a new dataset to the EventStore. Use it to plug externally fetched data into the store.
     *
     * NOTE: Dates, durations and relations (assignments, resources) on the events are determined async by a calculation
     * engine. Thus they cannot be directly accessed after assigning the new dataset.
     *
     * For example:
     *
     * ```javascript
     * eventStore.data = [{ startDate, duration }];
     * // eventStore.first.endDate is not yet calculated
     * ```
     *
     * To guarantee data is in a calculated state, wait for calculations for finish:
     *
     * ```javascript
     * eventStore.data = [{ startDate, duration }];
     * await eventStore.project.commitAsync();
     * // eventStore.first.endDate is calculated
     * ```
     *
     * Alternatively use `loadDataAsync()` instead:
     *
     * ```javascript
     * await eventStore.loadDataAsync([{ startDate, duration }]);
     * // eventStore.first.endDate is calculated
     * ```
     *
     * @member {EventModelConfig[]} data
     * @category Records
     */
    /**
     * Applies a new dataset to the EventStore and triggers calculations directly after. Use it to plug externally
     * fetched data into the store.
     *
     * ```javascript
     * await eventStore.loadDataAsync([{ startDate, duration }]);
     * // eventStore.first.endDate is calculated
     * ```
     *
     * @param {EventModelConfig[]} data Array of EventModel data objects
     * @function loadDataAsync
     * @category CRUD
     * @async
     */
    /**
     * Class used to represent records. Defaults to class EventModel.
     * @member {Scheduler.model.EventModel} modelClass
     * @typings {typeof EventModel}
     * @category Records
     */
    static get defaultConfig() {
      return {
        /**
         * CrudManager must load stores in the correct order. Lowest first.
         * @private
         */
        loadPriority: 100,
        /**
         * CrudManager must sync stores in the correct order. Lowest first.
         * @private
         */
        syncPriority: 200,
        id: "events",
        /**
         * Configure with `true` to also remove the event when removing the last assignment from the linked
         * AssignmentStore. This config has not effect when using EventStore in legacy `resourceId`-mode.
         * @config {Boolean}
         * @default
         * @category Common
         */
        removeUnassignedEvent: true,
        /**
         * Configure with `true` to force single-resource mode, an event can only be assigned to a single resource.
         * If not provided, the mode will be inferred from
         *
         * 1. presence of an assignment store (i.e. multi-assignment)
         * 2. presence of `resourceId` in the event store data (i.e. single assignment mode)
         * @config {Boolean}
         * @category Common
         */
        singleAssignment: null
      };
    }
    //region Init & destroy
    construct(config) {
      super.construct(config);
      this.autoTree = true;
      if (this.singleAssignment) {
        this.usesSingleAssignment = true;
      }
      if (!this.modelClass.isEventModel) {
        throw new Error("The model for the EventStore must subclass EventModel");
      }
    }
    //endregion
    //region Events records, iteration etc.
    /**
     * Returns a `Map`, keyed by `YYYY-MM-DD` date keys containing event counts for all the days
     * between the passed `startDate` and `endDate`. Occurrences of recurring events are included.
     *
     * Example:
     *
     * ```javascript
     *  eventCounts = eventStore.getEventCounts({
     *      startDate : scheduler.timeAxis.startDate,
     *      endDate   : scheduler.timeAxis.endDate
     *  });
     * ```
     *
     * @param {Object} options An options object determining which events to return
     * @param {Date} options.startDate The start date for the range of events to include.
     * @param {Date} [options.endDate] The end date for the range of events to include.
     * @category Events
     */
    getEventCounts(options) {
      const me = this, {
        filtersFunction,
        added
      } = me, result = me.getEvents({
        ...options,
        storeFilterFn: me.isFiltered ? me.reapplyFilterOnAdd ? filtersFunction : (eventRecord) => added.includes(eventRecord) ? me.indexOf(eventRecord) > -1 : filtersFunction(eventRecord) : null,
        dateMap: options.dateMap || true
      });
      result.forEach((value2, key) => result.set(key, value2.length));
      return result;
    }
    /**
     * Calls the supplied iterator function once for every scheduled event, providing these arguments
     * - event : the event record
     * - startDate : the event start date
     * - endDate : the event end date
     *
     * Returning false cancels the iteration.
     *
     * @param {Function} fn iterator function
     * @param {Object} [thisObj] `this` reference for the function
     * @category Events
     */
    forEachScheduledEvent(fn2, thisObj = this) {
      this.forEach((event) => {
        const { startDate, endDate } = event;
        if (startDate && endDate) {
          return fn2.call(thisObj, event, startDate, endDate);
        }
      });
    }
    /**
     * Returns an object defining the earliest start date and the latest end date of all the events in the store.
     *
     * @returns {Object} An object with 'startDate' and 'endDate' properties (or null values if data is missing).
     * @category Events
     */
    getTotalTimeSpan() {
      let earliest = new Date(9999, 0, 1), latest = /* @__PURE__ */ new Date(0);
      this.forEach((event) => {
        if (event.startDate) {
          earliest = DateHelper.min(event.startDate, earliest);
        }
        if (event.endDate) {
          latest = DateHelper.max(event.endDate, latest);
        }
      });
      earliest = earliest < new Date(9999, 0, 1) ? earliest : null;
      latest = latest > /* @__PURE__ */ new Date(0) ? latest : null;
      return this.lastTotalTimeSpan = {
        startDate: earliest || null,
        endDate: latest || earliest || null
      };
    }
    /**
     * Checks if given event record is persistable. By default it always is, override EventModels `isPersistable` if you
     * need custom logic.
     *
     * @param {Scheduler.model.EventModel} event
     * @returns {Boolean}
     * @category Events
     */
    isEventPersistable(event) {
      return event.isPersistable;
    }
    //endregion
    //region Resource
    /**
     * Filters the events associated with a resource, based on the function provided. An array will be returned for those
     * events where the passed function returns true.
     * @param {Scheduler.model.ResourceModel} resource
     * @param {Function} fn The function
     * @param {Object} [thisObj] `this` reference for the function
     * @returns {Scheduler.model.EventModel[]} the events in the time span
     * @private
     * @category Resource
     */
    filterEventsForResource(resource, fn2, thisObj = this) {
      return resource.getEvents(this).filter(fn2.bind(thisObj));
    }
    /**
     * Returns all resources assigned to an event.
     *
     * @param {Scheduler.model.EventModel|String|Number} event
     * @returns {Scheduler.model.ResourceModel[]}
     * @category Resource
     */
    getResourcesForEvent(event) {
      if (event.isOccurrence) {
        event = event.recurringTimeSpan;
      }
      return this.assignmentStore.getResourcesForEvent(event);
    }
    /**
     * Returns all events assigned to a resource.
     * *NOTE:* this does not include occurrences of recurring events. Use the
     * {@link Scheduler/data/mixin/GetEventsMixin#function-getEvents} API to include occurrences of recurring events.
     * @param {Scheduler.model.ResourceModel|String|Number} resource Resource or resource id.
     * @returns {Scheduler.model.EventModel[]}
     * @category Resource
     */
    getEventsForResource(resource) {
      return this.assignmentStore.getEventsForResource(resource);
    }
    //endregion
    //region Assignment
    /**
     * Returns all assignments for a given event.
     *
     * @param {Scheduler.model.EventModel} event The event to get assignments for
     * @returns {Scheduler.model.AssignmentModel[]} Array of assignments for the event
     * @category Assignment
     */
    getAssignmentsForEvent(event) {
      return this.assignmentStore.getAssignmentsForEvent(event) || [];
    }
    /**
     * Returns all assignments for a given resource.
     *
     * @param {Scheduler.model.ResourceModel|String|Number} resource
     * @returns {Scheduler.model.AssignmentModel[]}
     * @category Assignment
     */
    getAssignmentsForResource(resource) {
      return this.assignmentStore.getAssignmentsForResource(resource) || [];
    }
    /**
     * Creates and adds assignment record for a given event and a resource.
     *
     * @param {Scheduler.model.EventModel|String|Number} event
     * @param {Scheduler.model.ResourceModel|String|Number|Scheduler.model.ResourceModel[]|String[]|Number[]} resource The resource(s) to assign to the event
     * @param {Boolean} [removeExistingAssignments] `true` to first remove existing assignments
     * @returns {Scheduler.model.AssignmentModel[]} An array with the created assignment(s)
     * @category Assignment
     */
    assignEventToResource(event, resource, removeExistingAssignments = false) {
      return this.assignmentStore.assignEventToResource(event, resource, void 0, removeExistingAssignments);
    }
    /**
     * Removes assignment record for a given event and a resource.
     *
     * @param {Scheduler.model.EventModel|String|Number} event
     * @param {Scheduler.model.ResourceModel|String|Number} resource
     * @category Assignment
     */
    unassignEventFromResource(event, resource) {
      this.assignmentStore.unassignEventFromResource(event, resource);
    }
    /**
     * Reassigns an event from an old resource to a new resource
     *
     * @param {Scheduler.model.EventModel}    event    An event or id of the event to reassign
     * @param {Scheduler.model.ResourceModel|Scheduler.model.ResourceModel[]} oldResource A resource or id to unassign from
     * @param {Scheduler.model.ResourceModel|Scheduler.model.ResourceModel[]} newResource A resource or id to assign to
     * @category Assignment
     */
    reassignEventFromResourceToResource(event, oldResource, newResource) {
      const me = this, newResourceId = Model.asId(newResource), assignment = me.assignmentStore.getAssignmentForEventAndResource(event, oldResource);
      if (assignment) {
        assignment.resourceId = newResourceId;
      } else {
        me.assignmentStore.assignEventToResource(event, newResource);
      }
    }
    /**
     * Checks whether an event is assigned to a resource.
     *
     * @param {Scheduler.model.EventModel|String|Number} event
     * @param {Scheduler.model.ResourceModel|String|Number} resource
     * @returns {Boolean}
     * @category Assignment
     */
    isEventAssignedToResource(event, resource) {
      return this.assignmentStore.isEventAssignedToResource(event, resource);
    }
    /**
     * Removes all assignments for given event
     *
     * @param {Scheduler.model.EventModel|String|Number} event
     * @category Assignment
     */
    removeAssignmentsForEvent(event) {
      this.assignmentStore.removeAssignmentsForEvent(event);
    }
    /**
     * Removes all assignments for given resource
     *
     * @param {Scheduler.model.ResourceModel|String|Number} resource
     * @category Assignment
     */
    removeAssignmentsForResource(resource) {
      this.assignmentStore.removeAssignmentsForResource(resource);
    }
    //endregion
    /**
     * Appends a new record to the store
     * @param {Scheduler.model.EventModel} record The record to append to the store
     * @category CRUD
     */
    append(record) {
      return this.add(record);
    }
    //region Project
    get project() {
      return super.project;
    }
    set project(project) {
      var _a6, _b2;
      super.project = project;
      (_a6 = this.updateProject) == null ? void 0 : _a6.call(this, project);
      this.detachListeners("project");
      if (project) {
        if ((_b2 = project.assignmentStore) == null ? void 0 : _b2.isAssignmentStore) {
          this.attachToAssignmentStore(project.assignmentStore);
        }
        project.ion({
          name: "project",
          assignmentStoreChange: "onProjectAssignmentStoreChange",
          thisObj: this,
          prio: 200
          // Before UI updates
        });
      }
    }
    //endregion
    //region resource ids
    get usesResourceIds() {
      var _a6, _b2;
      return (_b2 = (_a6 = this.modelClass.fieldMap) == null ? void 0 : _a6.resourceIds.persist) != null ? _b2 : false;
    }
    //endregion
    //region Single assignment
    get usesSingleAssignment() {
      if (this.isChained) {
        return this.masterStore.usesSingleAssignment;
      }
      return this._usesSingleAssignment;
    }
    set usesSingleAssignment(value2) {
      this._usesSingleAssignment = value2;
      this.setupCheapAssignmentIdGeneration();
    }
    setupCheapAssignmentIdGeneration() {
      const { assignmentStore } = this;
      if ((assignmentStore == null ? void 0 : assignmentStore.isStore) && !assignmentStore.hasGenerateIdOverride) {
        assignmentStore.modelClass.generateId = function() {
          if (this.singleAssignmentIdCounter == null) {
            this.singleAssignmentIdCounter = 0;
          }
          return `a-${++this.singleAssignmentIdCounter}`;
        };
        assignmentStore.hasGenerateIdOverride = true;
      }
    }
    processRecords(eventRecords, onRecordCreated, silent) {
      var _a6;
      const { assignmentStore } = this, assignmentsToAdd = [];
      if (assignmentStore) {
        assignmentStore.skipInvalidateIndices = true;
      }
      eventRecords = super.processRecords(eventRecords, assignmentStore && !((_a6 = this.stm) == null ? void 0 : _a6.isRestoring) && ((eventRecord) => {
        var _a7;
        const resourceIds = this.usesResourceIds && ((_a7 = eventRecord.resourceIds) == null ? void 0 : _a7.length) ? eventRecord.resourceIds : [eventRecord.resourceId];
        if (!eventRecord.reassignedFromReplace && (resourceIds == null ? void 0 : resourceIds[0]) != null) {
          for (const resourceId of resourceIds) {
            if (!assignmentStore.includesAssignment(eventRecord.id, resourceId)) {
              assignmentsToAdd.push({
                id: assignmentStore.modelClass.generateId(""),
                resourceId,
                eventId: eventRecord.id
              });
            }
          }
        }
        eventRecord.reassignedFromReplace = false;
      }) || void 0);
      if (assignmentStore) {
        assignmentStore.storage.invalidateIndices();
        assignmentStore.skipInvalidateIndices = false;
        assignmentStore.add(assignmentsToAdd, silent);
      }
      return eventRecords;
    }
    joinRecordsToStore(records) {
      const { assignmentStore } = this;
      if (assignmentStore) {
        assignmentStore.skipInvalidateIndices = true;
        super.joinRecordsToStore(records);
        assignmentStore.storage.invalidateIndices();
        assignmentStore.skipInvalidateIndices = false;
      } else {
        super.joinRecordsToStore(records);
      }
    }
    processRecord(eventRecord, isDataset = false) {
      var _a6, _b2;
      eventRecord = super.processRecord(eventRecord, isDataset);
      const me = this, resourceId = eventRecord.get("resourceId"), { resourceIds } = eventRecord;
      if ((resourceIds == null ? void 0 : resourceIds.length) && me.usesResourceIds) {
        me.$processResourceIds = true;
        me.setupCheapAssignmentIdGeneration();
      } else if (resourceId != null && !eventRecord.meta.skipEnforcingSingleAssignment) {
        const existingRecord = me.getById(eventRecord.id), isReplacing = existingRecord && existingRecord !== eventRecord && !isDataset;
        if (isReplacing) {
          const assignmentStore = (_b2 = me.assignmentStore) != null ? _b2 : (_a6 = me.crudManager) == null ? void 0 : _a6.assignmentStore, assignmentSet = assignmentStore.storage.findItem("eventId", eventRecord.id);
          if (assignmentSet == null ? void 0 : assignmentSet.size) {
            const assignment = assignmentSet.values().next().value;
            assignment.resource = resourceId;
            eventRecord.reassignedFromReplace = true;
          }
        } else {
          me.$processResourceIds = true;
        }
        me.usesSingleAssignment = true;
      }
      return eventRecord;
    }
    processResourceIds() {
      var _a6, _b2, _c2;
      const me = this, assignmentStore = (_b2 = me.assignmentStore) != null ? _b2 : (_a6 = me.crudManager) == null ? void 0 : _a6.assignmentStore;
      if (me.$processResourceIds && (assignmentStore == null ? void 0 : assignmentStore.isAssignmentStore) && !(((_c2 = me.project) == null ? void 0 : _c2.isSharingAssignmentStore) && me.isChained)) {
        const assignments = [];
        me.forEach((eventRecord) => {
          const { resourceId, resourceIds, id: eventId } = eventRecord;
          if (resourceId != null) {
            assignments.push({
              id: assignmentStore.modelClass.generateId(""),
              resourceId,
              eventId
            });
          } else if (resourceIds == null ? void 0 : resourceIds.length) {
            resourceIds.forEach((rId) => {
              assignments.push({
                id: assignmentStore.modelClass.generateId(""),
                resourceId: rId,
                eventId
              });
            });
          }
        }, me, { includeFilteredOutRecords: true });
        assignmentStore.useRawData = {
          // for SchedulerPro assignment model is more complex than just event & resource
          // so we don't disable default values there
          disableDefaultValue: !me.modelClass.isProEventModel,
          disableDuplicateIdCheck: true,
          disableTypeConversion: true
        };
        assignmentStore.usesSingleAssignment = false;
        assignmentStore.verifyNoGeneratedIds = false;
        if (me.lazyLoad && assignmentStore.syncDataOnLoad) {
          assignmentStore.syncDataOnLoad = false;
        }
        assignmentStore.data = assignments;
        assignmentStore.usesSingleAssignment = true;
        me.$processResourceIds = false;
      }
    }
    loadTreeData() {
      super.loadTreeData(...arguments);
      this.processResourceIds();
    }
    loadData() {
      super.loadData(...arguments);
      this.processResourceIds();
    }
    // Optionally remove unassigned events
    onBeforeRemoveAssignment({ records }) {
      var _a6;
      const me = this;
      if (me.removeUnassignedEvent && !me.isRemoving && !me.isSettingData && !((_a6 = me.stm) == null ? void 0 : _a6.isRestoring) && !me.usesSingleAssignment && // Do not remove unassigned events when syncing data, new assignments etc. might be synced afterwards
      !me.assignmentStore.isSyncingDataOnLoad && !me.resourceStore.isSyncingDataOnLoad) {
        const toRemove = /* @__PURE__ */ new Set();
        records.forEach((assignmentRecord) => {
          const { event } = assignmentRecord;
          if (event && !event.isRemoved && event.assignments.every((a) => records.includes(a))) {
            toRemove.add(event);
          }
        });
        if (toRemove.size) {
          me.remove([...toRemove]);
        }
      }
    }
    onProjectAssignmentStoreChange({ store }) {
      this.attachToAssignmentStore(store);
    }
    attachToAssignmentStore(assignmentStore) {
      const me = this;
      me.detachListeners("assignmentStore");
      if (assignmentStore) {
        me.processResourceIds();
        assignmentStore.ion({
          name: "assignmentStore",
          // Adding an assignment in single assignment mode should set events resourceId if needed,
          // otherwise it should set events resourceIds (if persistable)
          addPreCommit({ records }) {
            if (!me.isSettingData && !me.isAssigning) {
              if (me.usesSingleAssignment) {
                records.forEach((assignment) => {
                  const { event } = assignment;
                  if ((event == null ? void 0 : event.isEvent) && event.resourceId !== assignment.resourceId) {
                    event.meta.isAssigning = true;
                    event.set("resourceId", assignment.resourceId);
                    event.meta.isAssigning = false;
                  }
                });
              } else if (me.usesResourceIds) {
                records.forEach((assignment) => {
                  var _a6;
                  const { event } = assignment;
                  if (event == null ? void 0 : event.isEvent) {
                    event.meta.isAssigning = true;
                    const resourceIds = (_a6 = event.resourceIds) != null ? _a6 : [];
                    if (!resourceIds.includes(assignment.resourceId)) {
                      event.resourceIds = [...resourceIds, assignment.resourceId];
                    }
                    event.meta.isAssigning = false;
                  }
                });
              } else {
                me.updateChainedStores();
              }
            }
          },
          // Called both for remove and removeAll
          beforeRemove: "onBeforeRemoveAssignment",
          // Removing an assignment in single assignment mode should set events resourceId to null,
          // otherwise it should set events resourceIds to an empty array
          removePreCommit({ records }) {
            if (!me.isSettingData && !me.isAssigning) {
              if (me.usesSingleAssignment) {
                records.forEach((assignment) => {
                  var _a6;
                  (_a6 = me.getById(assignment.eventId)) == null ? void 0 : _a6.set("resourceId", null);
                });
              } else if (me.usesResourceIds) {
                records.forEach(({ event, resourceId }) => {
                  var _a6;
                  const resourceIds = (_a6 = event.resourceIds) == null ? void 0 : _a6.slice(), indexToRemove = resourceIds == null ? void 0 : resourceIds.indexOf(resourceId);
                  if (indexToRemove >= 0) {
                    resourceIds.splice(indexToRemove, 1);
                    event.resourceIds = resourceIds;
                  }
                });
              } else {
                me.updateChainedStores();
                ;
              }
            }
          },
          removeAllPreCommit() {
            if (!me.isSettingData && !me.isAssigning) {
              if (me.usesSingleAssignment) {
                me.allRecords.forEach((eventRecord) => eventRecord.set("resourceId", null));
              } else if (me.usesResourceIds) {
                me.allRecords.forEach((eventRecord) => {
                  eventRecord.resourceIds = [];
                });
              } else {
                me.updateChainedStores();
              }
            }
          },
          // Keep events resourceId and resourceIds in sync with assignment on changes
          update({ record, changes }) {
            if ("resourceId" in changes) {
              const { event } = record;
              if (me.usesSingleAssignment) {
                event.meta.isAssigning = true;
                event.set("resourceId", changes.resourceId.value);
                event.meta.isAssigning = false;
              } else if (me.usesResourceIds) {
                event.meta.isAssigning = true;
                const resourceIds = event.resourceIds.slice(), indexToRemove = resourceIds == null ? void 0 : resourceIds.indexOf(changes.resourceId.oldValue);
                if (indexToRemove >= 0) {
                  resourceIds.splice(indexToRemove, 1);
                }
                if (!(resourceIds == null ? void 0 : resourceIds.includes(changes.resourceId.value))) {
                  resourceIds.push(changes.resourceId.value);
                  event.resourceIds = resourceIds;
                }
                event.meta.isAssigning = false;
              } else {
                me.updateChainedStores();
              }
            }
          },
          // Keep events resourceIds in sync with assignment on dataset loading
          change({ action, records }) {
            if (action === "dataset") {
              if (me.usesResourceIds) {
                records.forEach(({ event, resourceId }) => {
                  var _a6;
                  const resourceIds = (_a6 = event.resourceIds) != null ? _a6 : [];
                  if (!resourceIds.includes(resourceId)) {
                    resourceIds.push(resourceId);
                    event.meta.isAssigning = true;
                    event.setData("resourceIds", resourceIds);
                    event.meta.isAssigning = false;
                  }
                });
              }
              if (!me.usesSingleAssignment && !me.usesResourceIds) {
                me.updateChainedStores();
              }
            }
          },
          thisObj: me
        });
      }
    }
    set data(data) {
      const me = this;
      me.isSettingData = true;
      if (me.usesSingleAssignment && !me.syncDataOnLoad && !me.isChained) {
        me.assignmentStore.removeAll(true);
      }
      super.data = data;
      me.isSettingData = false;
    }
    // Override trigger to decorate update/change events with a flag if resourceId was the only thing changed, in which
    // case the change most likely can be ignored since the assignment will also change
    trigger(eventName, params) {
      var _a6;
      const { changes } = params || {};
      if (changes && "resourceId" in changes && Object.keys(changes).length === 1 && !((_a6 = this.stm) == null ? void 0 : _a6.isRestoring)) {
        params.isAssign = true;
      }
      return super.trigger(...arguments);
    }
    remove(records, ...args) {
      var _a6, _b2;
      const result = super.remove(records, ...args);
      if (result.length && this.usesSingleAssignment) {
        for (const eventRecord of result) {
          if (!eventRecord.isOccurrence) {
            (_b2 = this.assignmentStore || ((_a6 = this.crudManager) == null ? void 0 : _a6.assignmentStore)) == null ? void 0 : _b2.remove(eventRecord.assignments, true);
          }
        }
      }
      return result;
    }
    //endregion
  }, __publicField(_a5, "$name", "EventStoreMixin"), _a5;
};

// ../Scheduler/lib/Scheduler/data/mixin/GetEventsMixin.js
var returnTrue3 = () => true;
var notRecurring = (event) => !event.isRecurring;
var GetEventsMixin_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    constructor() {
      super(...arguments);
      __publicField(this, "bufferIsUnavailableTime", false);
    }
    /**
     * Returns an array of events for the date range specified by the `startDate` and `endDate` options.
     *
     * By default, for any date, this includes any event which *intersects* that date.
     *
     * To only include events that are fully contained *within* the date range, pass the `allowPartial`
     * option as `false`.
     *
     * By default, any occurrences of recurring events are included in the resulting array (not applicable in Gantt). If
     * that is not required, pass the `includeOccurrences` option as `false`. **Note that if `includeOccurrences` is
     * `true`, the start date and end date options are mandatory. The method must know what range of occurrences needs
     * to be generated and returned.**
     *
     * Example:
     *
     * ```javascript
     *  visibleEvents = eventStore.getEvents({
     *      resourceRecord : myResource,
     *      startDate      : scheduler.timeAxis.startDate,
     *      endDate        : scheduler.timeAxis.endDate
     *  });
     * ```
     *
     * @param {Object} options An options object determining which events to return
     * @param {Date} [options.date] If only one date is required, pass this option instead of the
     * `startDate` and `endDate` options.
     * @param {Date} options.startDate The start date for the range of events to include.
     * @param {Date} [options.endDate] The end date for the range of events to include.
     * @param {Scheduler.model.ResourceModel} [options.resourceRecord] Pass a resource to only return events assigned to
     *   this resource. Not supported when using the `dateMap` option (see below)
     * @param {Function} [options.filter] A function to filter out events which are not required.
     * @param {Boolean} [options.ignoreFilters] By default, store filters are honoured. Pass this
     * as `true` to include filtered out events.
     * @param {Boolean} [options.includeOccurrences=true] Occurrences of recurring events are included by default.
     * @param {Boolean} [options.allowPartial=true] Events which start before or after the range, but *intersect* the
     *   range are included by default.
     * @param {Boolean} [options.startOnly] Pass `true` to only include events which *start on* each date in the range.
     * @param {Boolean} [options.silent] Pass `true` to not fire the {@link #event-loadDateRange} event.
     * @param {Boolean} [options.onlyAssigned] Pass `true` to only include events that are assigned to a resource
     * @param {Boolean|Map} [options.dateMap] Populates the passed `Map`, or if passed as `true`, creates and
     * returns a new `Map`. The keys are `YYYY-MM-DD` date strings and the entries are arrays of
     * {@link Scheduler.model.EventModel}s.
     * @returns {Scheduler.model.EventModel[]|Map} Events which match the passed configuration.
     * @category Events
     */
    getEvents({
      filter: filter2,
      date: date2,
      startDate,
      // Events which intersect the startDate/endDate
      endDate,
      // will be returned
      startOnly,
      // Only events which start on each date will be returned
      includeOccurrences,
      // Interpolate occurrences into the returned event set
      allowPartial,
      // Include events which *intersect* the date range
      onlyAssigned = false,
      // Only include events that are assigned to a resource
      dateMap = false,
      // Return a Map keyed by date each value being an array of events
      dayTime = null,
      silent,
      // Private option. Select which date index to look up events in depending on the date
      // we are examining in the date iteration process. Some callers may want to use
      // different indices depending on the stage through the date iteration.
      // See Calendar package for usage.
      getDateIndex,
      fromRowHeight
    }) {
      const me = this, options = arguments[0], {
        lastDateRange,
        added,
        filtersFunction
      } = me, passedFilter = filter2;
      if (onlyAssigned) {
        options.filter = passedFilter ? (e) => passedFilter(e) && e.resources.length : (e) => e.resources.length;
      }
      if (!("startDate" in options)) {
        startDate = options.startDate = date2;
      }
      if (!("includeOccurrences" in options)) {
        includeOccurrences = options.includeOccurrences = true;
      }
      if (!("allowPartial" in options)) {
        allowPartial = options.allowPartial = !startOnly;
      }
      if (!("propagateRecurringEvents" in options)) {
        options.propagateRecurringEvents = true;
      }
      options.storeFilterFn = me.isFiltered && !options.ignoreFilters && !me.remoteFilter ? me.reapplyFilterOnAdd ? filtersFunction : (eventRecord) => added.includes(eventRecord) ? me.indexOf(eventRecord) > -1 : filtersFunction(eventRecord) : null;
      if (!endDate) {
        if (startDate) {
          endDate = options.endDate = DateHelper.clearTime(startDate);
          endDate.setDate(endDate.getDate() + 1);
        } else {
          if (includeOccurrences) {
            throw new Error("getEvents MUST be passed startDate and endDate if recurring occurrences are requested");
          }
          options.dateFilter = returnTrue3;
        }
      }
      if (!options.dateFilter) {
        if (startOnly) {
          options.dateFilter = (e) => {
            const eventStartDate = e.hasBatchedChange("startDate") ? e.get("startDate") : e.startDate;
            return eventStartDate && !(DateHelper.clearTime(eventStartDate) - startDate);
          };
        } else if (allowPartial) {
          options.dateFilter = (e) => {
            const eventStartDate = e.hasBatchedChange("startDate") ? e.get("startDate") : e.startDate, eventEndDate = e.hasBatchedChange("endDate") ? e.get("endDate") : e.endDate || eventStartDate, isMilestone = !(eventStartDate - eventEndDate);
            return eventStartDate && (isMilestone ? DateHelper.betweenLesserEqual(eventStartDate, startDate, endDate) : DateHelper.intersectSpans(eventStartDate, eventEndDate, startDate, endDate));
          };
        } else {
          options.dateFilter = (e) => {
            const eventStartDate = e.hasBatchedChange("startDate") ? e.get("startDate") : e.startDate, eventEndDate = e.hasBatchedChange("endDate") ? e.get("endDate") : e.endDate || eventStartDate;
            return eventStartDate && eventStartDate >= startDate && eventEndDate <= endDate;
          };
        }
      }
      const newDateRange = { startDate, endDate }, changed = Boolean(!lastDateRange || (lastDateRange.startDate - newDateRange.startDate || lastDateRange.endDate - newDateRange.endDate));
      me.processConfiguredListeners();
      if (!silent) {
        me.trigger("loadDateRange", {
          old: lastDateRange || {},
          new: Objects.clone(newDateRange),
          changed
        });
        me.lastDateRange = Objects.clone(newDateRange);
      }
      return dateMap ? me.getEventsAsMap(options) : me.getEventsAsArray(options);
    }
    /**
     * Internal implementation for {@link #function-getEvents} to use when not using dateMap.
     * @private
     */
    getEventsAsArray({
      filter: filter2,
      date: date2,
      resourceRecord,
      startDate = date2,
      // Events which intersect the startDate/endDate
      endDate,
      // will be returned
      startOnly,
      // Only events which start on each date will be returned
      includeOccurrences = true,
      // Interpolate occurrences into the returned event set
      dayTime = null,
      // Injected by the getEvents master method
      dateFilter,
      storeFilterFn,
      // Private option. Select which date index to look up events in depending on the date
      // we are examining in the date iteration process. Some callers may want to use
      // different indices depending on the stage through the date iteration.
      // See Calendar package for usage.
      getDateIndex
    }) {
      const me = this, events = [], count = storeFilterFn ? me.count : me.totalCount;
      if (count) {
        let candidateEvents = resourceRecord ? me.getEventsForResource(resourceRecord) : null;
        if (!resourceRecord) {
          const dateIndex = me.useDayIndex(dayTime), eventSet = /* @__PURE__ */ new Set(), indexName = startOnly ? "startDate" : "date";
          me.recurringEvents.forEach((e) => {
            if (dateIndex.dayTime.startOfDay(e.startDate) <= startDate) {
              eventSet.add(e);
            }
          });
          for (const date3 = new Date(startDate); date3 < endDate; date3.setDate(date3.getDate() + 1)) {
            const coincidingEvents = dateIndex.get(getDateIndex ? getDateIndex(date3) : indexName, date3);
            coincidingEvents == null ? void 0 : coincidingEvents.forEach((e) => eventSet.add(e));
          }
          candidateEvents = [...eventSet];
        }
        if (storeFilterFn) {
          candidateEvents = candidateEvents.filter(storeFilterFn);
        }
        for (let i = 0, { length } = candidateEvents; i < length; i++) {
          const e = candidateEvents[i];
          if (includeOccurrences && e.isRecurring) {
            events.push.apply(events, e.getOccurrencesForDateRange(startDate, endDate).filter(dateFilter));
          } else if (dateFilter(e)) {
            events.push(e);
          }
        }
      }
      return filter2 ? events.filter(filter2) : events;
    }
    /**
     * Internal implementation for {@link #function-getEvents} to use when using dateMap.
     * @private
     */
    getEventsAsMap({
      filter: passedFilter,
      date: date2,
      resourceRecord,
      // Not supported yet. Will add if ever requested.
      startDate = date2,
      // Events which intersect the startDate/endDate
      endDate,
      // will be returned
      startOnly,
      // Only events which start on each date will be returned
      includeOccurrences = true,
      // Interpolate occurrences into the returned event set
      propagateRecurringEvents = true,
      // Recurring events will be propagated to all dates they cover unless this is false
      dateMap,
      // Return a Map keyed by date each value being an array of events
      dayTime = null,
      storeFilterFn,
      // Private option. Select which date index to look up events in depending on the date
      // we are examining in the date iteration process. Some callers may want to use
      // different indices depending on the stage through the date iteration.
      // See Calendar package for usage.
      getDateIndex
    }) {
      var _a6;
      const me = this;
      if (dateMap == null ? void 0 : dateMap.clear) {
        dateMap.clear();
      } else {
        dateMap = /* @__PURE__ */ new Map();
      }
      if (me.count) {
        const dateIndex = me.useDayIndex(dayTime), indexName = startOnly ? "startDate" : "date", recurringEvents = [], filter2 = (e) => (!passedFilter || passedFilter(e)) && (!storeFilterFn || storeFilterFn(e)), baseEventFilter = (e) => notRecurring(e) && filter2(e);
        dayTime = dateIndex.dayTime;
        if (resourceRecord) {
          throw new Error("Querying for events for a resource and returning a date-keyed Map is not supported");
        } else {
          (_a6 = me.recurringEvents) == null ? void 0 : _a6.forEach((e) => {
            if (dayTime.startOfDay(e.startDate) < endDate) {
              recurringEvents.push(e);
            }
          });
          for (const date3 = new Date(startDate); date3 < endDate; date3.setDate(date3.getDate() + 1)) {
            let [coincidingEvents, key] = dateIndex.get(getDateIndex ? getDateIndex(date3) : indexName, date3, true);
            if (coincidingEvents == null ? void 0 : coincidingEvents.size) {
              coincidingEvents = [...coincidingEvents].filter(baseEventFilter);
              if (coincidingEvents.length) {
                (dateMap.get(key) || dateMap.set(key, []).get(key)).push(...coincidingEvents);
              }
            }
          }
        }
        for (let i = 0, { length } = recurringEvents; i < length; i++) {
          const e = recurringEvents[i], occurrences = (includeOccurrences ? e.getOccurrencesForDateRange(startDate, endDate) : [e]).filter(filter2), lastDate = DateHelper.add(endDate, 1, "day");
          for (let bucket, i2 = 0, { length: length2 } = occurrences; i2 < length2; i2++) {
            const occurrence = occurrences[i2], date3 = dayTime.startOfDay(occurrence.startDate), indexName2 = getDateIndex ? getDateIndex(date3) : startOnly ? "startDate" : "date", lastIntersectingDate = propagateRecurringEvents === false || indexName2 === "startDate" || !occurrence.durationMS ? DateHelper.add(date3, 1, "day") : DateHelper.min(occurrence.endDate || DateHelper.add(occurrence.startDate, occurrence.duration, occurrence.durationUnit), lastDate);
            for (; date3 < lastIntersectingDate; date3.setDate(date3.getDate() + 1)) {
              const key = dayTime.dateKey(date3);
              (bucket = dateMap.get(key)) || dateMap.set(key, bucket = []);
              bucket.push(occurrence);
            }
          }
        }
      }
      return dateMap;
    }
    /**
     * Checks if a date range is available for a given resource.
     *
     * Note that when asked to check a 0 duration range, any 0 duration events at the same point in time will be
     * considered overlapping.
     *
     * @param {Date} startDate The start date
     * @param {Date} endDate The end date
     * @param {Scheduler.model.EventModel|Scheduler.model.EventModel[]|null} excludeEvent An event to exclude from the check (or null)
     * @param {Scheduler.model.ResourceModel|Scheduler.model.ResourceModel[]} resourceRecord The resource
     * @returns {Boolean} True if the timespan is available for the resource
     * @category Resource
     */
    isDateRangeAvailable(startDate, endDate, excludeEvent, resourceRecord) {
      let allowOverlap;
      if (arguments.length === 1) {
        endDate = startDate.endDate;
        excludeEvent = startDate.excludeEvent;
        resourceRecord = startDate.resourceRecord;
        allowOverlap = startDate.allowOverlap;
        startDate = startDate.startDate;
      }
      resourceRecord = ArrayHelper.asArray(resourceRecord);
      return resourceRecord.every((resource) => this.isResourceAvailable(startDate, endDate, excludeEvent, resource, allowOverlap));
    }
    isResourceAvailable(startDate, endDate, excludeEvent, resourceRecord, allowOverlap) {
      var _a6;
      if (resourceRecord.data.generatedParent) {
        return false;
      }
      allowOverlap = typeof resourceRecord.allowOverlap === "boolean" ? resourceRecord.allowOverlap : allowOverlap;
      if (allowOverlap) {
        return true;
      }
      const events = this.getEventsForResource(resourceRecord);
      if (((_a6 = this.recurringEvents) == null ? void 0 : _a6.size) > 0) {
        events.push(...events.flatMap((event) => event.occurrences));
      }
      const allEvents = new Set(events);
      if (excludeEvent) {
        excludeEvent = ArrayHelper.asArray(excludeEvent);
        excludeEvent.forEach((event) => {
          if (event.isAssignment) {
            const currentEvent = event.event, resources = currentEvent.resources;
            resources.forEach((resource) => {
              if (resource.id !== event.resourceId) {
                this.getEventsForResource(resource).forEach((event2) => allEvents.add(event2));
              }
            });
          }
          const eventToRemove = event.isAssignment ? event.event : event;
          allEvents.delete(eventToRemove);
        });
      }
      const all = Array.from(allEvents);
      if (startDate.getTime() === endDate.getTime()) {
        return !all.some(
          (event) => event.isScheduled && (event.duration === 0 ? event.startDate.getTime() === startDate.getTime() : DateHelper.intersectSpans(startDate, endDate, event.startDate, event.endDate))
        );
      }
      const { bufferIsUnavailableTime } = this;
      return !all.some((event) => {
        const eventStart = bufferIsUnavailableTime && event.outerStartDate || event.startDate, eventEnd = bufferIsUnavailableTime && event.outerEndDate || event.endDate;
        return event.isScheduled && DateHelper.intersectSpans(startDate, endDate, eventStart, eventEnd);
      });
    }
  }, __publicField(_a5, "$name", "GetEventsMixin"), _a5;
};

// ../Scheduler/lib/Scheduler/data/util/EventDayIndex.js
var indexNameMap = {
  date: "_dateIndex",
  startDate: "_startDateIndex"
};
var indexProps = Object.values(indexNameMap);
var emptyArray12 = Object.freeze([]);
var { MILLIS_PER_DAY: MILLIS_PER_DAY2 } = DayTime;
var EventDayIndex = class {
  constructor(store, dayTime) {
    this.dayTime = dayTime || DayTime.MIDNIGHT;
    this.store = store;
    this.users = [this.dayTime];
  }
  /**
   * Adds an event record to the specified index (either "startDate" or "date") for a given `date`.
   * @param {String} indexName The index to which the event record is to be added (either "startDate" or "date").
   * @param {Date|Number} date A date for which the event record overlaps. The {@link Core.util.DayTime#function-dateKey}
   * method is used to convert this date to a "YYYY-MM-DD" key for the index.
   * @param {Scheduler.model.EventModel} eventRecord The event record.
   * @private
   */
  add(indexName, date2, eventRecord) {
    const index = this[indexNameMap[indexName]], key = this.dayTime.dateKey(date2), entry = index[key] || (index[key] = /* @__PURE__ */ new Set());
    entry.add(eventRecord);
  }
  /**
   * Adds an event record to all indexes for all dates which the event overlaps.
   * @param {Scheduler.model.EventModel} eventRecord The event record.
   * @private
   */
  addEvent(eventRecord) {
    var _a5, _b2, _c2;
    let dateMS = (_a5 = this.dayTime.startOfDay(eventRecord.startDate)) == null ? void 0 : _a5.getTime(), endDateMS;
    if (dateMS) {
      endDateMS = (_c2 = (_b2 = eventRecord.endDate) == null ? void 0 : _b2.getTime()) != null ? _c2 : dateMS;
      this.add("startDate", dateMS, eventRecord);
      do {
        this.add("date", dateMS, eventRecord);
        dateMS += MILLIS_PER_DAY2;
      } while (dateMS < endDateMS);
    }
  }
  /**
   * Clear this index.
   */
  clear() {
    indexProps.forEach((name) => this[name] = /* @__PURE__ */ Object.create(null));
  }
  /**
   * Returns an object that has properties named by the {@link Core.util.DayTime#function-dateKey} method, or the
   * array of event records if a `date` is specified, or the event record array and the date key in a 2-element array
   * if `returnKey` is `true`.
   * @param {String} indexName The name of the desired index (either 'date' or 'startDate').
   * @param {Number|Date} date The date as a `Date` or the millisecond UTC epoch. When passed, this method will return
   * the array of event records for this date.
   * @param {Boolean} [returnKey] Specify `true` to return the date key along with the event record array.
   * @returns {Object|Scheduler.model.EventModel[]}
   */
  get(indexName, date2, returnKey) {
    !this.initialized && this.initialize();
    let ret = this[indexNameMap[indexName]], key;
    if (date2) {
      key = this.dayTime.dateKey(date2);
      ret = returnKey ? [ret[key], key] : ret[key];
    }
    return ret;
  }
  /**
   * Called when this index is first used. Once called, further store changes will be used to maintain this index.
   * @private
   */
  initialize() {
    this.initialized = true;
    this.clear();
    this.sync("splice", this.store.storage.allValues);
  }
  invalidate() {
    this.initialized = false;
    indexProps.forEach((name) => this[name] = null);
  }
  /**
   * Returns `true` if the given `dayTime` matches this index.
   * @param {Core.util.DayTime} dayTime
   * @returns {Boolean}
   */
  matches(dayTime) {
    return this.dayTime.startShift === dayTime.startShift;
  }
  /**
   * Removes an event record from the specified index (either "startDate" or "date") for a given `date`.
   * @param {String} indexName The index to which the event record is to be removed (either "startDate" or "date").
   * @param {Date|Number} date A date for which the event record overlaps. The {@link Core.util.DayTime#function-dateKey}
   * method is used to convert this date to a "YYYY-MM-DD" key for the index.
   * @param {Scheduler.model.EventModel} eventRecord The event record.
   * @private
   */
  remove(indexName, date2, eventRecord) {
    const index = this[indexNameMap[indexName]], key = this.dayTime.dateKey(date2), entry = index[key];
    if (entry) {
      entry.delete(eventRecord);
    }
  }
  /**
   * Removes an event record from all indexes for all dates which the event overlaps.
   * @param {Scheduler.model.EventModel} eventRecord The event record.
   * @param {Date} startDate The start date for the event. This may be different from the `startDate` of the given
   * `eventRecord` when the event is rescheduled.
   * @param {Date} endDate The end date for the event. This may be different from the `endDate` of the given
   * `eventRecord` when the event is rescheduled.
   * @private
   */
  removeEvent(eventRecord, startDate, endDate) {
    var _a5, _b2;
    let dateMS = (_a5 = this.dayTime.startOfDay(startDate)) == null ? void 0 : _a5.getTime(), endDateMS;
    if (dateMS) {
      endDateMS = (_b2 = endDate == null ? void 0 : endDate.getTime()) != null ? _b2 : dateMS;
      this.remove("startDate", dateMS, eventRecord);
      do {
        this.remove("date", dateMS, eventRecord);
        dateMS += MILLIS_PER_DAY2;
      } while (dateMS < endDateMS);
    }
  }
  sync(action, added, removed, replaced, wasSet) {
    var _a5, _b2;
    added = added || emptyArray12;
    removed = removed || emptyArray12;
    const me = this, addedCount = added.length, removedCount = removed.length, replacedCount = replaced == null ? void 0 : replaced.length;
    let i, newEvent, outgoingEvent;
    if (!me.initialized) {
      return;
    }
    switch (action) {
      case "clear":
        me.clear();
        break;
      case "splice":
        if (replacedCount) {
          added = added.slice();
          removed = removed.slice();
          for (i = 0; i < replacedCount; i++) {
            removed.push(replaced[i][0]);
            added.push(replaced[i][1]);
          }
        }
        if (removedCount) {
          for (i = 0; i < removedCount; i++) {
            outgoingEvent = removed[i];
            me.removeEvent(outgoingEvent, outgoingEvent.startDate, outgoingEvent.endDate);
          }
        }
        if (addedCount) {
          for (i = 0; i < addedCount; i++) {
            newEvent = added[i];
            if (newEvent.isScheduled && !newEvent.isParent) {
              me.addEvent(newEvent);
            }
          }
        }
        break;
      case "reschedule":
        outgoingEvent = added[0];
        me.removeEvent(
          outgoingEvent,
          ((_a5 = wasSet.startDate) == null ? void 0 : _a5.oldValue) || outgoingEvent.startDate,
          ((_b2 = wasSet.endDate) == null ? void 0 : _b2.oldValue) || outgoingEvent.endDate
        );
        me.sync("splice", added);
        break;
    }
  }
  /**
   * This method registers a `dayTime` instance with this index in the `users` array.
   * @param {Core.util.DayTime} dayTime The instance to register.
   */
  register(dayTime) {
    this.users.push(dayTime);
  }
  /**
   * This method unregisters a `dayTime` instance, removing it from the `users` array. This method returns `true` if
   * this was the last registered instance and this index is no longer needed.
   * @param {Core.util.DayTime} dayTime The instance to register.
   * @returns {Boolean}
   */
  unregister(dayTime) {
    const { users } = this, i = users.indexOf(dayTime);
    if (i > -1) {
      users.splice(i, 1);
    }
    return !users.length;
  }
};
var proto3 = EventDayIndex.prototype;
indexProps.forEach((name) => proto3[name] = null);
proto3.initialized = false;
EventDayIndex._$name = "EventDayIndex";

// ../Scheduler/lib/Scheduler/data/mixin/DayIndexMixin.js
var { MIDNIGHT } = DayTime;
var DayIndexMixin_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    construct(config) {
      super.construct(config);
      this.dayIndices = null;
    }
    //region Keeping index in sync
    // Override to syncIndices on initial load
    afterLoadData() {
      var _a6;
      this.syncIndices("splice", this.storage.allValues);
      (_a6 = super.afterLoadData) == null ? void 0 : _a6.call(this);
    }
    /**
     * Responds to mutations of the underlying storage Collection.
     *
     * Maintain indices for fast finding of events by date.
     * @param {Object} event
     * @private
     */
    onDataChange({ action, added, removed, replaced }) {
      this.syncIndices(action, added, removed, replaced);
      super.onDataChange(...arguments);
    }
    onDataReplaced(action, data) {
      this.syncIndices("clear");
      this.syncIndices("splice", this.storage.values);
      super.onDataReplaced(action, data);
    }
    onModelChange(record, toSet, wasSet, silent, fromRelationUpdate) {
      if ("startDate" in wasSet || "endDate" in wasSet) {
        this.syncIndices("reschedule", [record], null, null, wasSet);
      }
      super.onModelChange(...arguments);
    }
    //endregion
    //region Index
    /**
     * Invalidates associated day indices.
     * @internal
     */
    invalidateDayIndices() {
      var _a6;
      (_a6 = this.dayIndices) == null ? void 0 : _a6.forEach((dayIndex) => dayIndex.invalidate());
    }
    /**
     * Registers a `DayTime` instance, creating an `EventDayIndex` for each distinct `startShift`. This index is
     * maintained until all instances with a matching `startShift` are {@link #function-unregisterDayIndex unregistered}.
     * @param {Core.util.DayTime} dayTime The instance to register.
     * @internal
     * @category Indexing
     */
    registerDayIndex(dayTime) {
      const me = this, dayIndices = me.dayIndices || (me.dayIndices = []);
      let dayIndex, i;
      for (i = 0; !dayIndex && i < dayIndices.length; ++i) {
        if (dayIndices[i].matches(dayTime)) {
          (dayIndex = dayIndices[i]).register(dayTime);
        }
      }
      !dayIndex && dayIndices.push(dayIndex = new EventDayIndex(me, dayTime));
      return dayIndex;
    }
    syncIndices(...args) {
      var _a6;
      (_a6 = this.dayIndices) == null ? void 0 : _a6.forEach((dayIndex) => dayIndex.sync(...args));
    }
    /**
     * Removes a registered `DayTime` instance. If this is the last instance registered to an `EventDayIndex`, that
     * index is removed.
     * @param {Core.util.DayTime} dayTime The instance to unregister.
     * @internal
     * @category Indexing
     */
    unregisterDayIndex(dayTime) {
      const me = this, { dayIndices } = me;
      for (let i = dayIndices == null ? void 0 : dayIndices.length; i-- > 0; ) {
        if (dayIndices[i].matches(dayTime)) {
          if (dayIndices[i].unregister(dayTime)) {
            dayIndices.splice(i, 1);
          }
          break;
        }
      }
    }
    /**
     * Returns the `EventDayIndex` to use for the given `DayTime` instance. This may be the primary instance or a
     * child instance created by {@link #function-registerDayIndex}.
     * @param {Core.util.DayTime} dayTime The `DayTime` of the desired index.
     * @returns {Scheduler.data.util.EventDayIndex}
     * @private
     * @category Indexing
     */
    useDayIndex(dayTime) {
      const me = this, { dayIndices } = me;
      dayTime = dayTime || MIDNIGHT;
      for (let i = 0; dayIndices && i < dayIndices.length; ++i) {
        if (dayIndices[i].matches(dayTime)) {
          return dayIndices[i];
        }
      }
      if (dayTime.startShift) {
        throw new Error(`No day index registered for ${dayTime} on ${me.id}`);
      }
      return me.registerDayIndex(MIDNIGHT);
    }
    //endregion
  }, __publicField(_a5, "$name", "DayIndexMixin"), _a5;
};

// ../Scheduler/lib/Scheduler/data/mixin/RecurringTimeSpansMixin.js
var emptyArray13 = Object.freeze([]);
var RecurringTimeSpansMixin_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    construct(...args) {
      const me = this;
      me.globalOccurrences = /* @__PURE__ */ new Map();
      me.recurringEvents = /* @__PURE__ */ new Set();
      me.ion({
        removeAll() {
          me.globalOccurrences.clear();
        }
      });
      super.construct(...args);
    }
    // Override to refreshRecurringEventsCache on initial load
    afterLoadData() {
      var _a6;
      this.globalOccurrences.clear();
      this.refreshRecurringEventsCache("clear");
      this.refreshRecurringEventsCache("splice", this.storage.allValues);
      (_a6 = super.afterLoadData) == null ? void 0 : _a6.call(this);
    }
    /**
     * Responds to mutations of the underlying storage Collection.
     *
     * Maintain indices for fast finding of events by date.
     * @param {Object} event
     * @private
     */
    onDataChange({ action, added, removed, replaced }) {
      this.refreshRecurringEventsCache(action, added, removed, replaced);
      super.onDataChange(...arguments);
    }
    refreshRecurringEventsCache(action, added = emptyArray13, removed = emptyArray13, replaced) {
      const me = this, { recurringEvents } = me, replacedCount = replaced == null ? void 0 : replaced.length;
      switch (action) {
        case "clear":
          recurringEvents.clear();
          break;
        case "splice": {
          if (replacedCount) {
            added = added.slice();
            removed = removed.slice();
            for (let i = 0; i < replacedCount; i++) {
              removed.push(replaced[i][0]);
              added.push(replaced[i][1]);
            }
          }
          const addedCount = added.length, removedCount = removed.length;
          if (removedCount && recurringEvents.size) {
            for (let i = 0; i < removedCount; i++) {
              recurringEvents.delete(removed[i]);
            }
          }
          if (addedCount) {
            for (let i = 0; i < addedCount; i++) {
              const newEvent = added[i];
              if (newEvent.isRecurring) {
                recurringEvents.add(newEvent);
              }
            }
          }
          break;
        }
      }
    }
    getById(id2) {
      let result = super.getById(id2);
      if (!result) {
        result = this.globalOccurrences.get(this.modelClass.asId(id2));
      }
      return result;
    }
    onModelChange(record, toSet, wasSet, silent, fromRelationUpdate) {
      const isRecurrenceRelatedFieldChange = !silent && this.isRecurrenceRelatedFieldChange(record, wasSet);
      if (isRecurrenceRelatedFieldChange) {
        record.removeOccurrences();
      }
      super.onModelChange(...arguments);
      if (isRecurrenceRelatedFieldChange) {
        const event = { action: "batch", records: this.storage.values };
        this.trigger("refresh", event);
        this.trigger("change", event);
      }
    }
    /**
     * The method restricts which field modifications should trigger timespan occurrences building.
     * By default, any field change of a recurring timespan causes the rebuilding.
     * @param  {Scheduler.model.TimeSpan} timeSpan The modified timespan.
     * @param  {Object} wasSet Object containing the change set.
     * @returns {Boolean} `True` if the fields modification should trigger the timespan occurrences rebuilding.
     * @internal
     * @category Recurrence
     */
    isRecurrenceRelatedFieldChange(timeSpan, wasSet) {
      return timeSpan.isRecurring || "recurrenceRule" in wasSet;
    }
    /**
     * Builds occurrences for the provided timespan across the provided date range.
     * @private
     * @category Recurrence
     */
    getOccurrencesForTimeSpan(timeSpan, startDate, endDate) {
      const result = [];
      if (timeSpan.isRecurring) {
        timeSpan.recurrence.forEachOccurrence(startDate, endDate, (r) => result.push(r));
      }
      return result;
    }
    set data(data) {
      this.globalOccurrences.clear();
      super.data = data;
    }
    /**
     * Returns all the recurring timespans.
     * @returns {Scheduler.model.TimeSpan[]} Array of recurring events.
     * @category Recurrence
     */
    getRecurringTimeSpans() {
      return [...this.recurringEvents];
    }
  }, __publicField(_a5, "$name", "RecurringTimeSpansMixin"), __publicField(_a5, "configurable", {
    /**
     * When a recurring event *base* is rescheduled, the new start date may
     * not conform with the recurrence pattern if for example certain weekdays
     * are selected, such as to repeat every Monday.
     *
     * By default, changing the {@link Scheduler.model.TimeSpan#field-startDate}
     * to a date that is *not* visited by the recurrence pattern does not recalculate that
     * pattern. It merely changes the date at which that pattern begins.
     *
     * So if an event is explicitly configured to repeat every Monday (The
     * {@link Scheduler.model.mixin.RecurringTimeSpan#field-recurrenceRule} contains `BYDAY=MO`)
     * and the base event is changed by any means to have its {@link Scheduler.model.TimeSpan#field-startDate}
     * on the following Tuesday, visually, the effect would be that the base event "disappears" and
     * the following occurrences *do not move*.
     *
     * The recurrence pattern still repeats every Monday. It's just that that pattern is
     * calculated starting from a different date.
     *
     * To have a rescheduling change to a recurring event *base* recalculate the recurrence
     * pattern to conform to the new start date - to include that date in the pattern - configure
     * `autoAdjustRecurrence` as `true`
     * @prp {Boolean}
     * @default false
     */
    autoAdjustRecurrence: null
  }), _a5;
};

// ../Scheduler/lib/Scheduler/data/mixin/RecurringEventsMixin.js
var RecurringEventsMixin_default = (Target) => {
  var _a5;
  return _a5 = class extends RecurringTimeSpansMixin_default(Target || Base) {
    /**
     * Returns all the recurring events.
     *
     * **An alias for ** {@link Scheduler.data.mixin.RecurringTimeSpansMixin#function-getRecurringTimeSpans} method.
     *
     * @returns {Scheduler.model.EventModel[]} Array of recurring events.
     * @category Recurrence
     */
    getRecurringEvents() {
      return this.getRecurringTimeSpans();
    }
    isEventPersistable(event) {
      return super.isEventPersistable(event) && (!event.supportsRecurring || !event.isOccurrence);
    }
  }, __publicField(_a5, "$name", "RecurringEventsMixin"), _a5;
};

// ../Scheduler/lib/Scheduler/data/util/recurrence/RecurrenceDayRuleEncoder.js
var dayParseRegExp = /^([+-]?[0-9])?(SU|MO|TU|WE|TH|FR|SA)$/;
var days = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
var RecurrenceDayRuleEncoder = class extends Base {
  static decodeDay(rawDay) {
    let parsedDay, result;
    if (parsedDay = dayParseRegExp.exec(rawDay)) {
      result = [days.indexOf(parsedDay[2])];
      if (parsedDay[1]) {
        parsedDay[1] = parseInt(parsedDay[1], 10);
        result.push(parsedDay[1]);
      }
    }
    return result;
  }
  static encodeDay(day2) {
    let position;
    if (Array.isArray(day2)) {
      [day2, position] = day2;
    }
    return (position ? position.toString() : "") + days[day2];
  }
  // Turns days values provided as an array of strings (like [`-1MO`, `SU`, `+3FR`])
  // into an array of [ dayIndex, position ] elements, where:
  //
  // - `dayIndex` - zero-based week day index value (0 - Sunday, 1 - Monday, 2 - Tuesday, etc.)
  // - `position` - (optional) 1-based position of the day (integer value (can be both positive and negative))
  static decode(rawDays) {
    const result = [];
    let parsedDay;
    if (rawDays) {
      for (let i = 0; i < rawDays.length; i++) {
        if (parsedDay = this.decodeDay(rawDays[i])) {
          result.push(parsedDay);
        }
      }
    }
    return result;
  }
  static encode(days2) {
    const result = [];
    let day2;
    if (days2) {
      for (let i = 0; i < days2.length; i++) {
        if (day2 = this.encodeDay(days2[i])) {
          result.push(day2);
        }
      }
    }
    return result;
  }
};
RecurrenceDayRuleEncoder._$name = "RecurrenceDayRuleEncoder";

// ../Scheduler/lib/Scheduler/data/util/recurrence/AbstractRecurrenceIterator.js
var frequencyToUnitRe = /ly$/i;
var frequencyToUnit = (frequency) => {
  const result = frequency.replace(frequencyToUnitRe, "");
  return result === "DAI" ? "DAY" : result;
};
var fn = (date2, counter, isFirst, timeSpan) => timeSpan.buildOccurrence(date2, isFirst);
var captureLastOccurrence = (date2) => lastOccurrenceDate = date2;
var lastOccurrenceDate;
var AbstractRecurrenceIterator = class extends Base {
  static processIterationConfig(config) {
    const { recurrence } = config, {
      frequency,
      interval,
      timeSpan,
      endDate: until,
      count
    } = recurrence;
    if (!config.syncingStartDate && !timeSpan.meta.isSyncedWithRule) {
      const intervalEndDate = DateHelper.add(timeSpan.startDate, interval, frequencyToUnit(frequency)), endDate2 = DateHelper.min(
        intervalEndDate,
        config.endDate || intervalEndDate
      );
      this.forEachDate({
        syncingStartDate: true,
        startDate: timeSpan.startDate,
        endDate: endDate2,
        recurrence,
        fn
      });
    }
    const timeSpanStart = timeSpan.startDate;
    let {
      startDate = timeSpanStart,
      endDate = until
    } = config;
    if (startDate < timeSpanStart) {
      startDate = timeSpanStart;
    }
    if (until) {
      if (!endDate || endDate > until) {
        endDate = until;
      }
    } else if (count && startDate > timeSpanStart) {
      this.forEachDate({
        recurrence,
        fn: captureLastOccurrence
      });
      if (!endDate || endDate > lastOccurrenceDate) {
        endDate = new Date(lastOccurrenceDate.getTime() + 1);
      }
    }
    const earliestVisibleDate = startDate;
    if (!config.startOnly) {
      startDate = new Date(DateHelper.max(DateHelper.add(startDate, -interval, frequencyToUnit(frequency)), timeSpanStart));
    }
    return Object.assign({
      extraArgs: [],
      // Only check start date for time spans with 0 duration
      startOnly: !Boolean(timeSpan.durationMS)
    }, config, {
      startDate,
      endDate,
      timeSpan,
      timeSpanStart,
      earliestVisibleDate,
      endDateMS: endDate == null ? void 0 : endDate.getTime(),
      timeSpanStartMS: timeSpanStart.getTime(),
      earliestVisibleDateMS: earliestVisibleDate.getTime(),
      durationMS: timeSpan.durationMS,
      spansStart: startDate <= timeSpanStart && endDate > timeSpanStart
    });
  }
  static getOccurrenceIndex(event) {
    if (event.isOccurrence) {
      return DateHelper.diff(event.recurringTimeSpan.startDate, event.startDate, frequencyToUnit(event.recurringTimeSpan.recurrence.frequency));
    }
  }
  /**
   * Returns Nth occurrence of a week day in the provided period of time.
   * @param  {Date} startDate Period start date.
   * @param  {Date} endDate   Period end date.
   * @param  {Number} day    Week day (0 - Sunday, 1 - Monday, 2 - Tuesday, etc.)
   * @param  {Number} index  Index to find.
   * @returns {Date}           Returns the found date or null if there is no `index`th entry.
   * @private
   */
  static getNthDayInPeriod(startDate, endDate, day2, index) {
    let result, sign, borderDate;
    if (index) {
      if (index > 0) {
        sign = 1;
        borderDate = startDate;
      } else {
        sign = -1;
        borderDate = endDate;
      }
      const delta = day2 - borderDate.getDay();
      if (sign * delta < 0) {
        index += sign;
      }
      result = new Date(borderDate);
      result.setDate(borderDate.getDate() + (index - sign) * 7 + delta);
      if (result < startDate || result > endDate) {
        result = null;
      }
    }
    return result;
  }
  static buildDate(year, month2, date2) {
    const dt = new Date(year, month2, date2);
    if (dt.getFullYear() === year && dt.getMonth() === month2 && dt.getDate() === date2) {
      return dt;
    }
  }
  static isValidPosition(position) {
    return Boolean(position);
  }
  static forEachDateAtPositions(dates, positions, fn2, scope) {
    const datesLength = dates.length, processed = {};
    for (let i = 0; i < positions.length; i++) {
      const index = positions[i];
      if (this.isValidPosition(index)) {
        const date2 = index > 0 ? dates[index - 1] : dates[datesLength + index];
        if (date2 && !processed[date2.getTime()]) {
          processed[date2.getTime()] = true;
          if (fn2.call(scope, date2) === false) {
            return false;
          }
        }
      }
    }
  }
  static isInView(startOnly, occurrenceDate, earliestVisibleDate, durationMS, timeSpan) {
    return (startOnly ? occurrenceDate >= earliestVisibleDate : occurrenceDate.valueOf() + durationMS > earliestVisibleDate) && !timeSpan.hasException(occurrenceDate);
  }
  // Slightly faster version of ^, used by Daily & Weekly iterators
  static isInViewMS(startOnly, occurrenceDate, occurrenceDateMS, earliestVisibleDateMS, durationMS, timeSpan) {
    return (startOnly ? occurrenceDateMS >= earliestVisibleDateMS : occurrenceDateMS + durationMS > earliestVisibleDateMS) && !timeSpan.hasException(occurrenceDate);
  }
};
__publicField(AbstractRecurrenceIterator, "frequency", "NONE");
__publicField(AbstractRecurrenceIterator, "MAX_OCCURRENCES_COUNT", 1e6);
AbstractRecurrenceIterator._$name = "AbstractRecurrenceIterator";

// ../Scheduler/lib/Scheduler/data/util/recurrence/DailyRecurrenceIterator.js
var DailyRecurrenceIterator = class extends AbstractRecurrenceIterator {
  /**
   * Iterates over the passed date range, calling the passed callback on each date on which
   * starts a recurring event which matches the passed recurrence rule and overlaps the start and end dates
   * and is not an {@link Scheduler.model.mixin.RecurringTimeSpan#field-exceptionDates exceptionDate}
   * in the recurring event.
   * @param {Object} config An object which describes how to iterate.
   * @param {Date} config.startDate The point in time to begin iteration.
   * @param {Date} config.endDate The point in time to end iteration.
   * @param {Boolean} [config.startOnly] By default, all occurrences which intersect the date range
   * will be visited. Pass `true` to only visit occurrences which *start* in the date range.
   * @param {Scheduler.model.RecurrenceModel} config.recurrence The point in time to end iteration.
   * @param {Function} config.fn The function to call for each date which matches the recurrence in the date range.
   * @param {Date} config.fn.date The occurrence date.
   * @param {Number} config.fn.counter A counter of how many dates have been visited in this iteration.
   * @param {Boolean} config.fn.isFirst A flag which is `true` if the date is the first occurrence in the specified recurrence rule.
   * @param {Array} [config.extraArgs] Extra arguments to pass to the callback after the `isFirst` argument.
   */
  static forEachDate(config) {
    const {
      startOnly,
      startDate,
      endDate,
      endDateMS,
      timeSpan,
      timeSpanStart,
      earliestVisibleDateMS,
      durationMS,
      spansStart,
      recurrence,
      fn: fn2,
      extraArgs,
      scope = this
    } = this.processIterationConfig(config), { interval } = recurrence, delay2 = startDate - timeSpanStart, intervalDuration = interval * 864e5, delayInIntervals = Math.floor(delay2 / intervalDuration);
    let { count } = recurrence, counter = 0, occurrenceDate = DateHelper.add(timeSpanStart, delayInIntervals * interval, "day"), occurrenceDateMS = occurrenceDate.getTime();
    if (!endDate && !count) {
      count = this.MAX_OCCURRENCES_COUNT;
    }
    while (!endDateMS || occurrenceDateMS < endDateMS) {
      const inView = this.isInViewMS(
        startOnly,
        occurrenceDate,
        occurrenceDateMS,
        earliestVisibleDateMS,
        durationMS,
        timeSpan
      );
      counter++;
      if (
        // Stop if:
        // - COUNT-limited and that value is collected
        // - or UNTIL-limited and that date is reached
        count && counter > count || endDateMS && occurrenceDateMS >= endDateMS || // otherwise if we are in the visible timespan
        // run the fn-callback and stop if it returns FALSE
        inView && fn2.apply(scope, [
          occurrenceDate,
          counter,
          counter === 1 && spansStart,
          timeSpan,
          ...extraArgs
        ]) === false
      ) {
        break;
      }
      occurrenceDate = DateHelper.add(occurrenceDate, interval, "day");
      occurrenceDateMS = occurrenceDate.getTime();
    }
  }
};
__publicField(DailyRecurrenceIterator, "frequency", "DAILY");
DailyRecurrenceIterator._$name = "DailyRecurrenceIterator";

// ../Scheduler/lib/Scheduler/data/util/recurrence/WeeklyRecurrenceIterator.js
var WeeklyRecurrenceIterator = class extends AbstractRecurrenceIterator {
  /**
   * Iterates over the passed date range, calling the passed callback on each date on which
   * starts an event which matches the passed recurrence rule and overlaps the start and end dates.
   * @param {Object} config An object which describes how to iterate.
   * @param {Date} config.startDate The point in time to begin iteration.
   * @param {Date} config.endDate The point in time to end iteration.
   * @param {Boolean} [config.startOnly] By default, all occurrences which intersect the date range
   * will be visited. Pass `true` to only visit occurrences which *start* in the date range.
   * @param {Scheduler.model.RecurrenceModel} config.recurrence The point in time to end iteration.
   * @param {Function} config.fn The function to call for each date which matches the recurrence in the date range.
   * @param {Date} config.fn.date The occurrence date.
   * @param {Number} config.fn.counter A counter of how many dates have been visited in this iteration.
   * @param {Boolean} config.fn.isFirst A flag which is `true` if the date is the first occurrence in the specified recurrence rule.
   * @param {Array} [config.extraArgs] Extra arguments to pass to the callback after the `isFirst` argument.
   */
  static forEachDate(config) {
    const {
      startOnly,
      startDate,
      endDateMS,
      timeSpan,
      timeSpanStart,
      timeSpanStartMS,
      earliestVisibleDateMS,
      durationMS,
      spansStart,
      recurrence,
      fn: fn2,
      extraArgs,
      scope = this
    } = this.processIterationConfig(config), {
      interval,
      days: days2
    } = recurrence, { weekStartDay } = DateHelper, startHours = timeSpanStart.getHours(), startMinutes = timeSpanStart.getMinutes(), startSeconds = timeSpanStart.getSeconds(), startMS = timeSpanStart.getMilliseconds();
    let counter = 0, { count } = recurrence, weekDays = RecurrenceDayRuleEncoder.decode(days2), weekStartDate, occurrenceDate;
    if (!(weekDays == null ? void 0 : weekDays.length)) {
      weekDays = [[timeSpanStart.getDay()]];
    }
    if (weekStartDay > 0) {
      for (let i = 0; i < weekDays.length; i++) {
        if (weekStartDay > weekDays[i][0]) {
          weekDays[i][0] = 7 - weekStartDay - weekDays[i][0];
        } else {
          weekDays[i][0] -= weekStartDay;
        }
      }
    }
    weekDays.sort((a, b) => a[0] - b[0]);
    weekStartDate = DateHelper.getNext(count || interval > 1 ? timeSpanStart : startDate, "week", 0);
    if (!endDateMS && !count) {
      count = this.MAX_OCCURRENCES_COUNT;
    }
    while (!endDateMS || weekStartDate.getTime() < endDateMS) {
      for (let i = 0; i < weekDays.length; i++) {
        occurrenceDate = new Date(
          weekStartDate.getFullYear(),
          weekStartDate.getMonth(),
          weekStartDate.getDate() + weekDays[i][0],
          startHours,
          startMinutes,
          startSeconds,
          startMS
        );
        const occurrenceDateMS = occurrenceDate.getTime();
        if (occurrenceDateMS >= timeSpanStartMS) {
          const inView = this.isInViewMS(
            startOnly,
            occurrenceDate,
            occurrenceDateMS,
            earliestVisibleDateMS,
            durationMS,
            timeSpan
          );
          counter++;
          if (
            // Stop if:
            // - COUNT-limited and that value is collected
            // - or UNTIL-limited and that date is reached
            count && counter > count || endDateMS && occurrenceDateMS >= endDateMS || // otherwise if we are in the visible timespan
            // run the fn-callback and stop if it returns FALSE
            inView && fn2.apply(scope, [
              occurrenceDate,
              counter,
              counter === 1 && spansStart,
              timeSpan,
              ...extraArgs
            ]) === false
          ) {
            return;
          }
        }
      }
      weekStartDate = DateHelper.getNext(weekStartDate, "week", interval);
    }
  }
};
__publicField(WeeklyRecurrenceIterator, "frequency", "WEEKLY");
WeeklyRecurrenceIterator._$name = "WeeklyRecurrenceIterator";

// ../Scheduler/lib/Scheduler/data/util/recurrence/MonthlyRecurrenceIterator.js
var MonthlyRecurrenceIterator = class extends AbstractRecurrenceIterator {
  static getNthDayOfMonth(date2, dayNum) {
    const daysInMonth = DateHelper.daysInMonth(date2);
    let result = null;
    if (dayNum && Math.abs(dayNum) <= daysInMonth) {
      result = new Date(date2.getFullYear(), date2.getMonth(), dayNum < 0 ? daysInMonth + dayNum + 1 : dayNum);
    }
    return result;
  }
  static isValidPosition(position) {
    return position && Math.abs(position) > 0 && Math.abs(position) <= 31;
  }
  /**
   * Iterates over the passed date range, calling the passed callback on each date on which
   * starts an event which matches the passed recurrence rule and overlaps the start and end dates.
   * @param {Object} config An object which describes how to iterate.
   * @param {Date} config.startDate The point in time to begin iteration.
   * @param {Date} config.endDate The point in time to end iteration.
   * @param {Boolean} [config.startOnly] By default, all occurrences which intersect the date range
   * will be visited. Pass `true` to only visit occurrences which *start* in the date range.
   * @param {Scheduler.model.RecurrenceModel} config.recurrence The point in time to end iteration.
   * @param {Function} config.fn The function to call for each date which matches the recurrence in the date range.
   * @param {Date} config.fn.date The occurrence date.
   * @param {Number} config.fn.counter A counter of how many dates have been visited in this iteration.
   * @param {Boolean} config.fn.isFirst A flag which is `true` if the date is the first occurrence in the specified recurrence rule.
   * @param {Array} [config.extraArgs] Extra arguments to pass to the callback after the `isFirst` argument.
   */
  static forEachDate(config) {
    const {
      startOnly,
      startDate,
      endDate,
      timeSpan,
      timeSpanStart,
      earliestVisibleDate,
      durationMS,
      spansStart,
      recurrence,
      fn: fn2,
      extraArgs,
      scope = this
    } = this.processIterationConfig(config), {
      interval,
      days: days2,
      count,
      positions
    } = recurrence, weekDays = RecurrenceDayRuleEncoder.decode(days2), hasPositions = positions && positions.length, processedDate = {};
    let { monthDays } = recurrence, counter = 0, weekDayPosition, monthStartDate, monthEndDate, dates, occurrenceDate, i;
    monthStartDate = DateHelper.startOf(count || interval > 1 ? timeSpanStart : startDate, "month");
    monthEndDate = new Date(DateHelper.getNext(monthStartDate, "month", 1) - 1);
    if (!(monthDays && monthDays.length) && !(weekDays && weekDays.length)) {
      monthDays = [timeSpanStart.getDate()];
    }
    if (weekDays && weekDays.length) {
      weekDays.forEach((day2) => {
        if (day2[1]) {
          weekDayPosition = weekDayPosition || {};
          weekDayPosition[day2[0]] = day2[1];
        }
      });
    }
    while ((!endDate || endDate > monthStartDate) && (!count || counter < count)) {
      dates = [];
      if (weekDays == null ? void 0 : weekDays.length) {
        weekDays.forEach((day2) => {
          const weekDay = day2[0];
          let from = 1, till = 53;
          if (day2[1]) {
            from = till = day2[1];
          }
          for (i = from; i <= till; i++) {
            if (occurrenceDate = this.getNthDayInPeriod(monthStartDate, monthEndDate, weekDay, i)) {
              occurrenceDate = DateHelper.copyTimeValues(occurrenceDate, timeSpanStart);
              if (!processedDate[occurrenceDate.getTime()]) {
                processedDate[occurrenceDate.getTime()] = true;
                dates.push(occurrenceDate);
              }
            }
          }
        });
        dates.sort((a, b) => a - b);
        if (!hasPositions) {
          for (i = 0; i < dates.length; i++) {
            occurrenceDate = dates[i];
            if (occurrenceDate >= timeSpanStart) {
              const inView = this.isInView(
                startOnly,
                occurrenceDate,
                earliestVisibleDate,
                durationMS,
                timeSpan
              );
              counter++;
              if (
                // Stop if:
                // - COUNT-limited and that value is collected
                // - or UNTIL-limited and that date is reached
                count && counter > count || endDate && occurrenceDate >= endDate || // otherwise if we are in the visible timespan
                // run the fn-callback and stop if it returns FALSE
                inView && fn2.apply(scope, [
                  occurrenceDate,
                  counter,
                  counter === 1 && spansStart,
                  timeSpan,
                  ...extraArgs
                ]) === false
              ) {
                return false;
              }
            }
          }
        }
      } else {
        const sortedMonthDates = [];
        for (i = 0; i < monthDays.length; i++) {
          if ((occurrenceDate = this.getNthDayOfMonth(monthStartDate, monthDays[i])) && !processedDate[occurrenceDate.getTime()]) {
            processedDate[occurrenceDate.getTime()] = true;
            sortedMonthDates.push(occurrenceDate);
          }
        }
        sortedMonthDates.sort((a, b) => a - b);
        for (i = 0; i < sortedMonthDates.length; i++) {
          occurrenceDate = DateHelper.copyTimeValues(sortedMonthDates[i], timeSpanStart);
          if (hasPositions) {
            dates.push(occurrenceDate);
          } else if (occurrenceDate >= timeSpanStart) {
            const inView = this.isInView(
              startOnly,
              occurrenceDate,
              earliestVisibleDate,
              durationMS,
              timeSpan
            );
            counter++;
            if (
              // Stop if:
              // - COUNT-limited and that value is collected
              // - or UNTIL-limited and that date is reached
              count && counter > count || endDate && occurrenceDate >= endDate || // otherwise if we are in the visible timespan
              // run the fn-callback and stop if it returns FALSE
              inView && fn2.apply(scope, [
                occurrenceDate,
                counter,
                counter === 1 && spansStart,
                timeSpan,
                ...extraArgs
              ]) === false
            ) {
              return;
            }
          }
        }
      }
      if (hasPositions && dates.length) {
        if (this.forEachDateAtPositions(dates, positions, (occurrenceDate2) => {
          if (occurrenceDate2 >= timeSpanStart) {
            const inView = startOnly ? occurrenceDate2 >= earliestVisibleDate : occurrenceDate2.valueOf() + durationMS > earliestVisibleDate && !timeSpan.hasException(occurrenceDate2);
            counter++;
            if (
              // Stop if:
              // - COUNT-limited and that value is collected
              // - or UNTIL-limited and that date is reached
              count && counter > count || // Ignore dates outside of the [startDate, endDate] range
              endDate && occurrenceDate2 >= endDate || inView && // return false if it's time to stop recurring
              fn2.apply(scope, [
                occurrenceDate2,
                counter,
                counter === 1 && spansStart,
                timeSpan,
                ...extraArgs
              ]) === false
            ) {
              return false;
            }
          }
        }) === false) {
          return false;
        }
      }
      monthStartDate = DateHelper.getNext(monthStartDate, "month", interval);
      monthEndDate = new Date(DateHelper.getNext(monthStartDate, "month", 1) - 1);
    }
  }
};
__publicField(MonthlyRecurrenceIterator, "frequency", "MONTHLY");
MonthlyRecurrenceIterator._$name = "MonthlyRecurrenceIterator";

// ../Scheduler/lib/Scheduler/data/util/recurrence/YearlyRecurrenceIterator.js
var YearlyRecurrenceIterator = class extends AbstractRecurrenceIterator {
  /**
   * Iterates over the passed date range, calling the passed callback on each date on which
   * starts an event which matches the passed recurrence rule and overlaps the start and end dates.
   * @param {Object} config An object which describes how to iterate.
   * @param {Date} config.startDate The point in time to begin iteration.
   * @param {Date} config.endDate The point in time to end iteration.
   * @param {Boolean} [config.startOnly] By default, all occurrences which intersect the date range
   * will be visited. Pass `true` to only visit occurrences which *start* in the date range.
   * @param {Scheduler.model.RecurrenceModel} config.recurrence The point in time to end iteration.
   * @param {Function} config.fn The function to call for each date which matches the recurrence in the date range.
   * @param {Date} config.fn.date The occurrence date.
   * @param {Number} config.fn.counter A counter of how many dates have been visited in this iteration.
   * @param {Boolean} config.fn.isFirst A flag which is `true` if the date is the first occurrence in the specified recurrence rule.
   * @param {Array} [config.extraArgs] Extra arguments to pass to the callback after the `isFirst` argument.
   */
  static forEachDate(config) {
    const {
      startOnly,
      startDate,
      endDate,
      timeSpan,
      timeSpanStart,
      earliestVisibleDate,
      durationMS,
      spansStart,
      recurrence,
      fn: fn2,
      extraArgs,
      scope = this
    } = this.processIterationConfig(config), {
      interval,
      days: days2,
      count,
      positions
    } = recurrence, weekDays = RecurrenceDayRuleEncoder.decode(days2), hasPositions = positions == null ? void 0 : positions.length, processedDate = {};
    let { months } = recurrence, counter = 0, i, occurrenceDate, dates, intervalStartDate, intervalEndDate, weekDayPosition;
    intervalStartDate = DateHelper.startOf(count || interval > 1 ? timeSpanStart : startDate, "year");
    intervalEndDate = new Date(DateHelper.getNext(intervalStartDate, "year", 1) - 1);
    months == null ? void 0 : months.sort((a, b) => a - b);
    if (!(months == null ? void 0 : months.length) && !(weekDays == null ? void 0 : weekDays.length)) {
      months = [timeSpanStart.getMonth() + 1];
    }
    weekDays == null ? void 0 : weekDays.forEach((day2) => {
      if (day2[1]) {
        weekDayPosition = weekDayPosition || {};
        weekDayPosition[day2[0]] = day2[1];
      }
    });
    while ((!endDate || endDate > intervalStartDate) && (!count || counter < count)) {
      dates = [];
      if ((days2 == null ? void 0 : days2.length) && (months == null ? void 0 : months.length)) {
        for (const month2 of months) {
          intervalStartDate = new Date(intervalStartDate.getFullYear(), month2 - 1);
          intervalEndDate = new Date(intervalStartDate.getFullYear(), month2, -1);
          const monthIteratorConfig = {
            ...config,
            fn: (occurrenceDate2, _index, spansStart2, ...args) => {
              if (occurrenceDate2 >= timeSpanStart) {
                const inView = this.isInView(startOnly, occurrenceDate2, earliestVisibleDate, durationMS, timeSpan);
                counter++;
                if (inView) {
                  return fn2.apply(scope, [occurrenceDate2, counter, counter === 1 && spansStart2, ...args]);
                }
              }
            },
            startDate: intervalStartDate,
            endDate: intervalEndDate,
            recurrence: recurrence.constructor.new({
              timeSpan: recurrence.timeSpan,
              frequency: "MONTHLY",
              days: days2,
              positions
            })
          };
          MonthlyRecurrenceIterator.forEachDate(monthIteratorConfig);
        }
      } else {
        if (weekDays == null ? void 0 : weekDays.length) {
          weekDays.forEach((day2) => {
            const weekDay = day2[0];
            let from = 1, till = 53;
            if (day2[1]) {
              from = till = day2[1];
            }
            for (i = from; i <= till; i++) {
              if (occurrenceDate = this.getNthDayInPeriod(intervalStartDate, intervalEndDate, weekDay, i)) {
                occurrenceDate = DateHelper.copyTimeValues(occurrenceDate, timeSpanStart);
                if (!processedDate[occurrenceDate.getTime()]) {
                  processedDate[occurrenceDate.getTime()] = true;
                  dates.push(occurrenceDate);
                }
              }
            }
          });
          dates.sort((a, b) => a - b);
          if (!hasPositions) {
            for (i = 0; i < dates.length; i++) {
              occurrenceDate = dates[i];
              if (occurrenceDate >= timeSpanStart) {
                const inView = this.isInView(startOnly, occurrenceDate, earliestVisibleDate, durationMS, timeSpan);
                counter++;
                if (count && counter > count || endDate && occurrenceDate >= endDate || inView && fn2.apply(scope, [
                  occurrenceDate,
                  counter,
                  counter === 1 && spansStart,
                  timeSpan,
                  ...extraArgs
                ]) === false) {
                  return;
                }
              }
            }
          }
        } else {
          for (i = 0; i < months.length; i++) {
            if (occurrenceDate = this.buildDate(intervalStartDate.getFullYear(), months[i] - 1, timeSpanStart.getDate())) {
              occurrenceDate = DateHelper.copyTimeValues(occurrenceDate, timeSpanStart);
              if (!processedDate[occurrenceDate.getTime()]) {
                processedDate[occurrenceDate.getTime()] = true;
                if (hasPositions) {
                  dates.push(occurrenceDate);
                } else if (occurrenceDate >= timeSpanStart) {
                  const inView = startOnly ? occurrenceDate >= earliestVisibleDate : occurrenceDate.valueOf() + durationMS > earliestVisibleDate && !timeSpan.hasException(occurrenceDate);
                  counter++;
                  if (count && counter > count || endDate && occurrenceDate >= endDate || inView && fn2.apply(scope, [
                    occurrenceDate,
                    counter,
                    counter === 1 && spansStart,
                    timeSpan,
                    ...extraArgs
                  ]) === false) {
                    return;
                  }
                }
              }
            }
          }
        }
        if (hasPositions && dates.length) {
          if (this.forEachDateAtPositions(dates, positions, (occurrenceDate2) => {
            if (occurrenceDate2 >= timeSpanStart) {
              const inView = startOnly ? occurrenceDate2 >= earliestVisibleDate : occurrenceDate2.valueOf() + durationMS > earliestVisibleDate && !timeSpan.hasException(occurrenceDate2);
              counter++;
              if (inView && (!endDate || occurrenceDate2 < endDate)) {
                if (fn2.apply(scope, [occurrenceDate2, counter, counter === 1 && spansStart, timeSpan, ...extraArgs]) === false || count && counter >= count) {
                  return false;
                }
              }
            }
          }) === false) {
            return false;
          }
        }
      }
      intervalStartDate = DateHelper.getNext(intervalStartDate, "year", interval);
      intervalEndDate = new Date(DateHelper.getNext(intervalStartDate, "year", 1) - 1);
    }
  }
};
__publicField(YearlyRecurrenceIterator, "frequency", "YEARLY");
YearlyRecurrenceIterator._$name = "YearlyRecurrenceIterator";

// ../Scheduler/lib/Scheduler/model/RecurrenceModel.js
var recurrenceIterators = {};
[DailyRecurrenceIterator, WeeklyRecurrenceIterator, MonthlyRecurrenceIterator, YearlyRecurrenceIterator].forEach((it) => {
  recurrenceIterators[it.frequency] = it;
});
var frequencyToLater = {
  DAILY: "days",
  WEEKLY: "weeks",
  MONTHLY: "months",
  YEARLY: "years"
};
var dayNameToLater = {
  MO: "Mon",
  TU: "Tue",
  WE: "Wed",
  TH: "Thu",
  FR: "Fri",
  SA: "Sat",
  SU: "Sun"
};
function convertStringOfIntegerItemsValue(value2) {
  if (value2) {
    if (typeof value2 == "string") {
      value2 = value2.split(",").map((item) => parseInt(item, 10));
    }
  } else {
    value2 = null;
  }
  return value2;
}
function convertStringOfItemsValue(value2) {
  if (value2) {
    if (typeof value2 == "string") {
      value2 = value2.split(",");
    }
  } else {
    value2 = null;
  }
  return value2;
}
function isEqualAsString(value1, value2) {
  return String(value1) === String(value2);
}
function convertInteger(value2) {
  if (this.defaultValue && value2 === void 0) {
    return this.defaultValue;
  }
  if (this.allowNull && value2 == null) {
    return null;
  }
  value2 = parseInt(value2);
  return isNaN(value2) ? void 0 : value2;
}
var RecurrenceModel = class extends Model {
  //endregion Fields
  //region Construction
  construct(data = {}) {
    const me = this, {
      rule,
      timeSpan,
      laterJsSchedule
    } = data;
    me._suspendedTimeSpanNotifying = 0;
    delete data.timeSpan;
    delete data.rule;
    delete data.laterJsSchedule;
    super.construct(...arguments);
    if (rule) {
      me.suspendTimeSpanNotifying();
      me.rule = rule;
      me.resumeTimeSpanNotifying();
    }
    me.timeSpan = timeSpan;
    if (laterJsSchedule) {
      me.laterJsSchedule = laterJsSchedule;
    }
  }
  static processData(data, updating, store, record, forceUseRaw) {
    const { fieldMap } = this, { name, dataSource } = fieldMap.date;
    let date2 = data[dataSource || name];
    if (typeof date2 === "string") {
      date2 = fieldMap.date.convert(date2, data, record);
    }
    if (date2) {
      Object.assign(data, this.getDateValues(date2));
      data[dataSource || name] = date2;
    }
    return super.processData(data, updating, store, record, forceUseRaw);
  }
  //endregion Construction
  get dateFormat() {
    return this._dateFormat || "YYYYMMDDTHHmmss";
  }
  set dateFormat(format2) {
    this._dateFormat = format2;
  }
  get recurrenceIterator() {
    return recurrenceIterators[this.frequency];
  }
  /**
   * The timespan this recurrence is associated with.
   * @property {Scheduler.model.TimeSpan}
   */
  get timeSpan() {
    return this._timeSpan;
  }
  set timeSpan(value2) {
    this._timeSpan = value2;
  }
  get timeSpanDate() {
    var _a5;
    return (_a5 = this.timeSpan) == null ? void 0 : _a5.startDate;
  }
  static getDateValues(date2) {
    if (date2) {
      return {
        days: [RecurrenceDayRuleEncoder.encodeDay(date2.getDay())],
        monthDays: [date2.getDate()],
        months: [date2.getMonth() + 1]
      };
    }
  }
  getDateValues(date2) {
    date2 = this.date || this.timeSpanDate;
    return this.constructor.getDateValues(date2);
  }
  getRRule(includeTimeSpanData = false) {
    var _a5;
    const me = this, date2 = me.date || me.timeSpanDate, result = [];
    if (me.frequency) {
      result.push(`FREQ=${me.frequency}`);
      let { days: days2, monthDays, months } = me;
      if (includeTimeSpanData && date2) {
        const {
          days: defaultDays,
          monthDays: defaultMonthDays,
          months: defaultMonths
        } = me.constructor.getDateValues(date2);
        switch (me.frequency) {
          case "WEEKLY":
            if (!days2) {
              days2 = defaultDays;
            }
            break;
          case "MONTHLY":
            if (!(monthDays == null ? void 0 : monthDays.length)) {
              monthDays = defaultMonthDays;
            }
            break;
          case "YEARLY":
            if (!(monthDays == null ? void 0 : monthDays.length)) {
              monthDays = defaultMonthDays;
            }
            if (!(months == null ? void 0 : months.length)) {
              months = defaultMonths;
            }
            break;
        }
      }
      if (me.interval > 1) {
        result.push(`INTERVAL=${me.interval}`);
      }
      if (days2 == null ? void 0 : days2.length) {
        result.push("BYDAY=" + days2.join(","));
      }
      if (monthDays == null ? void 0 : monthDays.length) {
        result.push("BYMONTHDAY=" + monthDays.join(","));
      }
      if (months == null ? void 0 : months.length) {
        result.push("BYMONTH=" + months.join(","));
      }
      if (me.count) {
        result.push(`COUNT=${me.count}`);
      }
      if (me.endDate) {
        result.push("UNTIL=" + DateHelper.format(me.endDate, me.dateFormat));
      }
      if ((_a5 = me.positions) == null ? void 0 : _a5.length) {
        result.push("BYSETPOS=" + me.positions.join(","));
      }
      if (me.timeZone != null) {
        result.push("TZID=" + me.timeZone);
      }
    }
    return result.join(";");
  }
  /**
   * The recurrence rule. A string in [RFC-5545](https://tools.ietf.org/html/rfc5545#section-3.3.10) described format
   * ("RRULE" expression).
   * @property {String}
   */
  get rule() {
    return this.getRRule();
  }
  set rule(rule) {
    const me = this, values = {
      frequency: null,
      interval: null,
      count: null,
      endDate: null,
      days: null,
      monthDays: null,
      months: null,
      positions: null,
      timeZone: null
    };
    me.beginBatch();
    if (rule) {
      const parts = rule.split(";");
      for (let i = 0, len = parts.length; i < len; i++) {
        const part = parts[i].split("="), value2 = part[1];
        switch (part[0]) {
          case "FREQ":
            values.frequency = value2;
            break;
          case "INTERVAL":
            values.interval = value2;
            break;
          case "COUNT":
            values.count = value2;
            values.until = null;
            break;
          case "UNTIL":
            if (value2) {
              values.endDate = DateHelper.parse(value2, me.dateFormat);
            } else {
              values.endDate = null;
            }
            values.count = null;
            break;
          case "BYDAY":
            values.days = value2;
            break;
          case "BYMONTHDAY":
            values.monthDays = value2;
            break;
          case "BYMONTH":
            values.months = value2;
            break;
          case "BYSETPOS":
            values.positions = value2;
            break;
          case "TZID":
            values.timeZone = value2;
            break;
        }
      }
    }
    me.set(values);
    if (rule) {
      me.sanitize();
    }
    me.endBatch();
  }
  /**
   * Iterate occurrences for the owning timespan across the specified date range. This method can be called even
   * if the timespan is not yet a member of a store, however, the occurrences returned will not be cached across
   * subsequent calls to this method.
   * @param {Date} startDate The start date of the iteration.
   * @param {Date} endDate The end date of the iteration.
   * @param {Function} fn The function to call for each occurrence.
   * @param {Scheduler.model.TimeSpan} fn.occurrence The occurrence.
   * @param {Boolean} fn.first A flag which is `true` for the first occurrence of this recurrence.
   * @param {Number} fn.counter A counter of how many dates have been visited in this iteration.
   * @param {Date} fn.date The occurrence date.
   * @internal
   */
  forEachOccurrence(startDate, endDate, fn2) {
    if (this.timeSpanDate) {
      this.recurrenceIterator.forEachDate({
        recurrence: this,
        startDate,
        endDate,
        fn(date2, counter, first, timeSpan) {
          return fn2(timeSpan.buildOccurrence(date2, first), first, counter, date2);
        }
      });
    }
  }
  /**
   * Cleans up fields that do not make sense for the current {@link #field-frequency} value.
   * @private
   */
  sanitize() {
    var _a5, _b2;
    const me = this, { timeSpanDate } = me, values = {};
    me.isSanitizingSuspended = true;
    switch (me.frequency) {
      case "DAILY":
        values.positions = null;
        values.days = null;
        values.monthDays = null;
        values.months = null;
        break;
      case "WEEKLY": {
        values.positions = null;
        values.monthDays = null;
        values.months = null;
        const { days: days2 } = me;
        if (timeSpanDate && (days2 == null ? void 0 : days2.length) === 1 && days2[0] === RecurrenceDayRuleEncoder.encodeDay(timeSpanDate.getDay())) {
          values.days = null;
        }
        break;
      }
      case "MONTHLY": {
        if ((_a5 = me.monthDays) == null ? void 0 : _a5.length) {
          values.positions = null;
          values.days = null;
        }
        values.months = null;
        const { monthDays } = me;
        if (timeSpanDate && (monthDays == null ? void 0 : monthDays.length) === 1 && monthDays[0] === timeSpanDate.getDate()) {
          values.monthDays = null;
        }
        break;
      }
      case "YEARLY": {
        const { months } = me, dayCount = ((_b2 = this.days) == null ? void 0 : _b2.length) || 0;
        if (dayCount === 7) {
          values.days = null;
        }
        if (timeSpanDate && (months == null ? void 0 : months.length) === 1 && !(dayCount && dayCount < 7) && months[0] === timeSpanDate.getMonth() + 1) {
          values.months = null;
        }
        break;
      }
    }
    me.set(values);
    me.isSanitizingSuspended = false;
  }
  copy(...args) {
    const result = super.copy(...args);
    result.dateFormat = this.dateFormat;
    result.timeSpan = this.timeSpan;
    return result;
  }
  afterChange(toSet, wasSet, silent) {
    const result = super.afterChange(toSet, wasSet, silent), { timeSpan } = this;
    if (!this.isSanitizingSuspended) {
      this.sanitize();
    }
    if (timeSpan) {
      timeSpan.sanitizeRecurrenceData(this);
      if (!this.isTimeSpanNotifyingSuspended) {
        timeSpan.onRecurrenceChanged(this, ...arguments);
      }
    }
    return result;
  }
  setValue(fieldName, value2) {
    if (fieldName === "monthDays" && Number.isInteger(value2)) {
      value2 = [value2];
    }
    return super.setValue(fieldName, value2);
  }
  set(field, value2, ...args) {
    const values = typeof field === "object" ? field : { [field]: value2 };
    if (values.count) {
      values.endDate = null;
    } else if (values.endDate) {
      values.count = null;
    }
    return super.set(values, void 0, ...args);
  }
  get isTimeSpanNotifyingSuspended() {
    return Boolean(this._suspendedTimeSpanNotifying);
  }
  suspendTimeSpanNotifying() {
    this._suspendedTimeSpanNotifying++;
  }
  resumeTimeSpanNotifying() {
    if (this._suspendedTimeSpanNotifying)
      this._suspendedTimeSpanNotifying--;
  }
  reset() {
    this.allFields.forEach(({ name }) => this.set(name, null));
  }
  set laterJsSchedule(schedule) {
    var _a5;
    schedule = ((_a5 = schedule == null ? void 0 : schedule.schedules) == null ? void 0 : _a5[0]) || schedule;
    if (!schedule) {
      return;
    }
    this.reset();
    const {
      s,
      // seconds - 0-59
      m,
      // minutes - 0-59
      h,
      // hours - 0-23
      t,
      // time - seconds from midnight
      D,
      // day of month 1-based (0 means last day of month)
      d,
      // day of week 1-7
      // dc, // Nth day of week in a month (like 2nd Thursday)
      // dy, // day of year 1-based
      // wm, // week of month 1-based (0 means last week)
      // wy, // week of year 1-based (0 means last week)
      M
      //, // month 1-12
      // Y // year
    } = schedule;
    const data = { interval: 1 };
    if (s || m || h || t) {
      data.frequency = "DAILY";
    }
    if (d) {
      data.frequency = "WEEKLY";
      data.days = d.map((d2) => RecurrenceDayRuleEncoder.encodeDay(d2 - 1));
    }
    if (D) {
      data.frequency = "MONTHLY";
      data.monthDays = D.map((d2) => d2 || -1);
    }
    if (M) {
      data.frequency = "YEARLY";
      data.months = M.slice();
    }
    Object.assign(this, data);
  }
  get laterJsSchedule() {
    const { frequency, interval, days: days2, monthDays, months, positions } = this;
    let result = interval > 1 ? `every ${interval} ${frequencyToLater[frequency]} ` : "";
    switch (this.frequency) {
      case "YEARLY":
        if (months == null ? void 0 : months.length) {
          result += `on the ${months.join(",")} month `;
        }
      case "MONTHLY":
        if (monthDays == null ? void 0 : monthDays.length) {
          result += `on the ${monthDays.join(",")} day `;
          break;
        }
      case "WEEKLY":
        if (days2 == null ? void 0 : days2.length) {
          result += `on ${days2.map((d) => dayNameToLater[d]).join(",")} `;
        }
        if (positions == null ? void 0 : positions.length) {
          result += `on the ${positions.join(",")} day instance `;
        }
    }
    return result.trim();
  }
};
__publicField(RecurrenceModel, "$name", "RecurrenceModel");
//region Fields
__publicField(RecurrenceModel, "fields", [
  { name: "date", type: "date" },
  /**
   * Field defines the recurrence frequency. Supported values are: `DAILY`, `WEEKLY`, `MONTHLY`, `YEARLY`.
   * @field {'DAILY'|'WEEKLY'|'MONTHLY'|'YEARLY'} frequency
   */
  { name: "frequency", defaultValue: "DAILY" },
  /**
   * Field defines how often the recurrence repeats.
   * For example, if the recurrence is weekly its interval is 2, then the timespan repeats every two weeks.
   * @field {Number} interval
   */
  { name: "interval", defaultValue: 1, convert: convertInteger },
  /**
   * End date of the recurrence. Specifies when the recurrence ends.
   * The value is optional, the recurrence can as well be stopped using {@link #field-count} field value.
   * @field {Date} endDate
   */
  { name: "endDate", type: "date" },
  /**
   * Specifies the number of occurrences after which the recurrence ends.
   * The value includes the associated timespan itself so values less than 2 make no sense.
   * The field is optional, the recurrence as well can be stopped using {@link #field-endDate} field value.
   * @field {Number} count
   */
  { name: "count", allowNull: true, convert: convertInteger },
  /**
   * Specifies days of the week on which the timespan should occur.
   * An array of string values `SU`, `MO`, `TU`, `WE`, `TH`, `FR`, `SA`
   * corresponding to Sunday, Monday, Tuesday, Wednesday, Thursday, Friday, and Saturday days of the week.
   * Each value can also be preceded by a positive (+n) or negative (-n) integer.
   * If present, this indicates the nth occurrence of a specific day within the monthly or yearly recurrence.
   *
   * **Not applicable** for daily {@link #field-frequency}.
   * @field {String[]} days
   */
  {
    name: "days",
    convert: convertStringOfItemsValue,
    isEqual: isEqualAsString
  },
  /**
   * Specifies days of the month on which the timespan should occur.
   * An array of integer values (-31..-1 - +1..+31, negative values mean counting backwards from the month end).
   * **Applicable only** for monthly and yearly {@link #field-frequency}.
   * @field {Number[]} monthDays
   */
  {
    name: "monthDays",
    convert: convertStringOfIntegerItemsValue,
    isEqual: isEqualAsString
  },
  /**
   * Specifies months of the year on which the timespan should occur.
   * An array of integer values (1 - 12).
   * **Applicable only** for yearly {@link #field-frequency}.
   * @field {Number[]} months
   */
  {
    name: "months",
    convert: convertStringOfIntegerItemsValue,
    isEqual: isEqualAsString
  },
  /**
   * The positions to include in the recurrence. The values operate on a set of recurrence instances **in one interval** of the recurrence rule.
   * An array of integer values (valid values are 1 to 366 or -366 to -1, negative values mean counting backwards from the end of the built list of occurrences).
   * **Not applicable** for daily {@link #field-frequency}.
   * @field {Number[]} positions
   */
  {
    name: "positions",
    convert: convertStringOfIntegerItemsValue,
    isEqual: isEqualAsString
  },
  /**
   * Set to a IANA time zone (i.e. `Europe/Stockholm`) to force the occurrences to be calculated in that
   * configured time zone.
   *
   * Configuring this as a {@link #property-rule}, looks like this:
   * ```javascript
   * recurrenceRule = 'FREQ=DAILY;TZID=Europe/Stockholm';
   * ```
   */
  { name: "timeZone", allowNull: true }
]);
RecurrenceModel._$name = "RecurrenceModel";

// ../Scheduler/lib/Scheduler/model/mixin/RecurringTimeSpan.js
function convertExceptionDatesValue(value2) {
  const result = {}, { dateFormat } = this;
  if (value2) {
    value2 = typeof value2 == "string" ? value2.split(",") : ArrayHelper.asArray(value2);
    value2.forEach((item) => {
      if (typeof item == "string") {
        item = DateHelper.parse(item, dateFormat);
      }
      if (!isNaN(item)) {
        result[DateHelper.makeKey(item)] = 1;
      }
    });
  }
  return result;
}
function serializeExceptionDatesValue(value2) {
  const result = [], { dateFormat } = this;
  for (const date2 in value2) {
    if (value2[date2]) {
      result.push(DateHelper.format(DateHelper.parseKey(date2), dateFormat));
    }
  }
  return result;
}
var emptyArray14 = [];
var RecurringTimeSpan_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || TimeSpan) {
    /**
     * Returns `true` if this timespan supports recurring.
     * @property {Boolean}
     * @category Recurrence
     */
    get supportsRecurring() {
      return true;
    }
    /**
     * Override of {@link Core/data/Model}'s method. If an {@link #property-isOccurrence} is passed, it is detached from
     * its parent recurring event. If it still has a recurrence then the recurring event is changed to stop at the
     * occurrence date. If it has no recurrence an exception is added at the occurrence date.
     * @category Recurrence
     */
    remove() {
      if (this.isOccurrence) {
        const me = this, { recurringTimeSpan } = me;
        me.cancelBatch();
        recurringTimeSpan.beginBatch();
        me.detachFromRecurringEvent();
        recurringTimeSpan.endBatch();
      } else {
        return super.remove(...arguments);
      }
    }
    get eventStore() {
      var _a6, _b2;
      let result = this.isOccurrence && ((_a6 = this.recurringEvent) == null ? void 0 : _a6.eventStore) || super.eventStore;
      if (!result && ((_b2 = this.firstStore) == null ? void 0 : _b2.isRecurringTimeSpansMixin)) {
        result = this.firstStore;
      }
      return result;
    }
    /**
     * Name of the class representing the recurrence model, defaults to {@link Scheduler.model.RecurrenceModel}
     * @property {String}
     * @category Recurrence
     */
    get recurrenceModel() {
      return this._recurrenceModel || RecurrenceModel;
    }
    set recurrenceModel(model) {
      this._recurrenceModel = model;
    }
    /**
     * Sets a recurrence for the timespan with a given frequency, interval, and end.
     * @param {RecurrenceModelConfig|String|Scheduler.model.RecurrenceModel} recurrence A data object for an instance of
     * {@link Scheduler.model.RecurrenceModel}. May also be the frequency string: `DAILY`, `WEEKLY`, `MONTHLY`, or
     * `YEARLY`.
     *
     * ```javascript
     * // repeat the event every other week till Jan 2 2039
     * event.setRecurrence({
     *     frequency : "WEEKLY",
     *     interval  : 2,
     *     endDate   : new Date(2039, 0, 2)
     * });
     * ```
     *
     * Also a {@link Scheduler.model.RecurrenceModel recurrence model} can be provided as the only argument for this
     * method:
     *
     * ```javascript
     * const recurrence = new RecurrenceModel({ frequency : 'DAILY', interval : 5 });
     *
     * event.setRecurrence(recurrence);
     * ```
     *
     * or
     *
     * ```javascript
     * event.setRecurrence("WEEKLY", 2, new Date(2039, 0, 2));
     * ```
     * @param {Number} [interval] The interval between occurrences (instances of this recurrence). For example, a daily
     * recurrence with an interval of 2 occurs every other day. Must be greater than 0.
     * @param {Number|Date} [recurrenceEnd] The end of the recurrence. The value can be specified by a date or by a
     * maximum count of occurrences (has to greater than 1, since 1 means the event itself).
     * @category Recurrence
     */
    setRecurrence(recurrence, interval, recurrenceEnd) {
      const me = this;
      let recurrenceRule;
      if (recurrence) {
        if (!recurrence.isRecurrenceModel) {
          if (typeof recurrence === "string") {
            recurrence = {
              frequency: recurrence
            };
            if (interval) {
              recurrence.interval = interval;
            }
            if (recurrenceEnd) {
              if (recurrenceEnd instanceof Date) {
                recurrence.endDate = recurrenceEnd;
              } else {
                recurrence.count = recurrenceEnd;
              }
            }
          }
          recurrence = new me.recurrenceModel(recurrence);
        }
        recurrence.timeSpan = me;
        recurrenceRule = recurrence.rule;
      }
      me.recurrence = recurrence;
      me.recurrenceRule = recurrenceRule;
    }
    /**
     * The recurrence model used for the timespan.
     * @property {Scheduler.model.RecurrenceModel}
     * @category Recurrence
     */
    get recurrence() {
      const me = this, rule = me.recurrenceRule;
      if (!me._recurrence && rule) {
        me._recurrence = new me.recurrenceModel({ rule, timeSpan: me, id: `${me.id}-recurrence` });
      }
      return me._recurrence;
    }
    set recurrence(recurrence) {
      const me = this;
      me._recurrence = recurrence;
      if (recurrence) {
        recurrence.timeSpan = me;
        me.recurrenceRule = recurrence.rule;
      } else {
        if (me.isOccurrence) {
          me.setData("recurrenceRule", null);
        } else {
          me.recurrenceRule = null;
        }
      }
    }
    /**
     * Indicates if the timespan is recurring.
     * @property {Boolean}
     * @readonly
     * @category Recurrence
     */
    get isRecurring() {
      return Boolean(!this.isOccurrence && this.recurrence);
    }
    /**
     * Indicates if the timespan is an occurrence of another recurring timespan.
     * @property {Boolean}
     * @readonly
     * @category Recurrence
     */
    get isOccurrence() {
      return Boolean(this.recurringTimeSpan);
    }
    /**
     * The "main" timespan this model is an occurrence of. For non-occurrences returns `null`.
     * @property {Scheduler.model.TimeSpan}
     * @readonly
     * @internal
     * @category Recurrence
     */
    get recurringTimeSpan() {
      return this._recurringTimeSpan;
    }
    get isPersistable() {
      return super.isPersistable && (!this.supportsRecurring || !this.isOccurrence);
    }
    set recurringTimeSpan(recurringTimeSpan) {
      this._recurringTimeSpan = recurringTimeSpan;
    }
    /**
     * Returns the occurrences of this event over the specified time range. If the first
     * occurrence is in the time range `*this*` record is included in that position.
     * @param {Date} startDate The start date of the range for which to include occurrences.
     * @param {Date} [endDate] The end date of the range for which to include occurrences.
     * Defaults to the startDate.
     * @returns {Scheduler.model.TimeSpan[]} The array of occurrences which occur over the specified range.
     * @category Recurrence
     */
    getOccurrencesForDateRange(startDate, endDate = startDate) {
      return this.eventStore.getOccurrencesForTimeSpan(this, startDate, endDate);
    }
    /**
     * Array of this recurring timespan's cached occurrences. __Not including the owning recurring
     * event__.
     *
     * Empty if the timespan is not recurring.
     *
     * __Note that this is an internal accessor and is cleared whenever changes are made to the
     * owning recurring event__.
     * @property {Scheduler.model.TimeSpan[]}
     * @readonly
     * @internal
     * @category Recurrence
     */
    get occurrences() {
      if (this.isRecurring) {
        const result = [];
        this.occurrenceMap.forEach((occurrence) => {
          if (occurrence !== this) {
            result.push(occurrence);
          }
        });
        return result;
      }
      return emptyArray14;
    }
    /**
     * A Map, keyed by each date an occurrence intersects, of occurrences of this event.
     * @property {Map}
     * @readonly
     * @internal
     * @category Recurrence
     */
    get occurrenceMap() {
      return this._occurrencesMap || (this._occurrencesMap = /* @__PURE__ */ new Map());
    }
    /**
     * Removes an occurrence from this recurring timespan's cached occurrences.
     * @param dateOrTimeSpan occurrence date or occurrence TimeSpan
     * @internal
     * @category Recurrence
     */
    removeOccurrence(dateOrTimeSpan, eventStore = this.eventStore) {
      const date2 = dateOrTimeSpan.isTimeSpan ? dateOrTimeSpan.occurrenceDate : dateOrTimeSpan;
      eventStore == null ? void 0 : eventStore.globalOccurrences.delete(this.createRecurrenceKey(date2));
      this.occurrenceMap.delete(DateHelper.makeKey(date2));
    }
    /**
     * Removes all cached occurrences on or after the passed date from this recurring timespan's cached occurrences.
     * @internal
     * @category Recurrence
     */
    removeOccurrencesFrom(date2) {
      this.occurrenceMap.forEach((occurrence, dateKey) => {
        if (DateHelper.parseKey(dateKey) >= date2) {
          this.removeOccurrence(occurrence);
        }
      });
    }
    /**
     * Removes this recurring timespan's cached occurrences.
     * @internal
     * @category Recurrence
     */
    removeOccurrences(eventStore) {
      [this, ...this.occurrences].forEach((occurrence) => this.removeOccurrence(occurrence, eventStore));
    }
    /**
     * The method is triggered when the timespan recurrence settings get changed.
     * It updates the {@link #field-recurrenceRule} field in this case.
     * @internal
     * @category Recurrence
     */
    onRecurrenceChanged() {
      var _a6;
      this.recurrenceRule = ((_a6 = this.recurrence) == null ? void 0 : _a6.rule) || null;
    }
    sanitizeRecurrenceData(recurrence = this.recurrence) {
      this.removeOccurrences();
      if (recurrence.endDate) {
        const endDate = DateHelper.clearTime(recurrence.endDate), { exceptionDates } = this;
        if (exceptionDates) {
          for (const dateKey in exceptionDates) {
            const exceptionDate = DateHelper.parseKey(dateKey);
            if (exceptionDate >= endDate) {
              delete exceptionDates[dateKey];
            }
          }
        }
      }
    }
    /**
     * The original {@lScheduler.model.TimeSpan#field-startDate startDate} of this event before any modifications
     * took place. Used by {@link #function-removeOccurrence} and {@link #function-detachFromRecurringEvent}
     * @internal
     * @readonly
     * @category Recurrence
     */
    get occurrenceDate() {
      var _a6;
      return ((_a6 = this.meta.modified) == null ? void 0 : _a6.startDate) || this.startDate;
    }
    /**
     * If this event is an {@link #property-isOccurrence occurrence} of a recurring event, then this
     * property yields its zero-based occurrence index in the sequence.
     * @property {Number}
     * @readonly
     * @category Recurrence
     */
    get occurrenceIndex() {
      return AbstractRecurrenceIterator.getOccurrenceIndex(this);
    }
    /**
     * Builds an occurrence of this recurring event by cloning the timespan data.
     * The method is used internally by the __RecurringTimeSpans__ mixin.
     * Override it if you need to customize the generated occurrences.
     *
     * If the date requested is the start date of the event sequence, `this`
     * record is returned. All runs of recurring events begin with the base record.
     * @param  {Date} occurrenceDate The occurrence start date.
     * @param  {Boolean} isFirst `true` if this is the first occurrence.
     * @returns {Scheduler.model.TimeSpan} The occurrence.
     * @internal
     * @category Recurrence
     */
    buildOccurrence(occurrenceDate, isFirst) {
      var _a6, _b2, _c2;
      const me = this, {
        occurrenceMap,
        recurrence,
        meta
      } = me, globalOccurrences = (_a6 = me.eventStore) == null ? void 0 : _a6.globalOccurrences, occurrenceKey = DateHelper.makeKey(occurrenceDate), id2 = me.createRecurrenceKey(occurrenceDate, occurrenceKey), onStartDate = !(occurrenceDate - me.startDate), { fieldMap } = me.constructor;
      let occurrence = globalOccurrences == null ? void 0 : globalOccurrences.get(id2), { duration } = me;
      if (me.endDate && (me.allDay || !duration)) {
        duration = DateHelper.as(me.durationUnit, me.endDate.getTime() - me.startDate.getTime());
      }
      const occurrenceEndDate = duration !== void 0 ? DateHelper.add(occurrenceDate, duration, me.durationUnit) : void 0;
      if (!occurrence) {
        if (isFirst || onStartDate) {
          occurrence = me;
          if (!onStartDate && ((_b2 = me.recurrence) == null ? void 0 : _b2.timeZone) == null) {
            me.preventRecurrenceSync = true;
            me._preventInvalidation = (_c2 = me.project) == null ? void 0 : _c2.isWritingData;
            me.setStartEndDate(occurrenceDate, occurrenceEndDate, true);
            me._preventInvalidation = false;
            me.preventRecurrenceSync = false;
            recurrence.suspendTimeSpanNotifying();
            recurrence.sanitize();
            recurrence.resumeTimeSpanNotifying();
          }
          meta.isSyncedWithRule = true;
        } else {
          occurrence = me.copy(
            {
              [fieldMap.id.dataSource]: id2,
              [fieldMap.startDate.dataSource]: occurrenceDate,
              [fieldMap.endDate.dataSource]: occurrenceEndDate,
              [fieldMap.duration.dataSource]: duration,
              constraintDate: null,
              constraintType: null
            },
            { creatingOccurrence: true }
          );
          occurrence.recurringTimeSpan = me;
        }
        globalOccurrences == null ? void 0 : globalOccurrences.set(id2, occurrence);
        occurrenceMap.set(occurrenceKey, occurrence);
      }
      return occurrence;
    }
    createRecurrenceKey(date2 = this.startDate, dateKey = null) {
      return `_generated:${this.id}:${dateKey || DateHelper.makeKey(date2)}`;
    }
    // Converts this occurrence to a new "master" event
    convertToRealEvent(wasSet, silent) {
      var _a6;
      if (!this.isOccurrence) {
        return;
      }
      const me = this, {
        recurringTimeSpan,
        resources,
        occurrenceIndex,
        recurrence
      } = me, count = recurrence && recurringTimeSpan.recurrence.count, newResource = ((_a6 = wasSet == null ? void 0 : wasSet.resourceRecords) == null ? void 0 : _a6.value) || me.data.newResource;
      recurringTimeSpan.beginBatch();
      me.detachFromRecurringEvent();
      me.internalClearChanges();
      me._id = me.generateId(recurringTimeSpan.eventStore);
      me.setData("id", me._id);
      if (newResource) {
        delete me.data.resourceId;
      }
      recurringTimeSpan.eventStore.add(me, silent);
      if (count) {
        me.recurrence.count = count - occurrenceIndex;
      }
      if (newResource || (resources == null ? void 0 : resources.length)) {
        me.assign(newResource || resources);
      }
      if (newResource) {
        delete me.data.resourceRecords;
      }
      if (wasSet) {
        delete wasSet.resourceRecords;
      }
      recurringTimeSpan.endBatch();
    }
    afterChange(toSet, wasSet, silent, ...args) {
      var _a6, _b2, _c2, _d, _e;
      const me = this, { eventStore } = me;
      if ("recurrenceRule" in wasSet) {
        me._recurrence = null;
        eventStore == null ? void 0 : eventStore.recurringEvents[wasSet.recurrenceRule.value ? "add" : "delete"](me);
      }
      if (me.isOccurrence) {
        me.convertToRealEvent(wasSet, silent);
      } else {
        if (!me.preventRecurrenceSync && "startDate" in wasSet && me.isRecurring && (eventStore == null ? void 0 : eventStore.autoAdjustRecurrence)) {
          const { recurrence } = me, { startDate } = me, oldStartDate = wasSet.startDate.oldValue, oldMonthDate = startDate.getDate(oldStartDate), oldMonthWeek = Math.ceil(oldMonthDate / 7), monthDate = startDate.getDate(), dayNumber = startDate.getDay(), dayName2 = RecurrenceDayRuleEncoder.encodeDay(dayNumber), monthNumber = startDate.getMonth() + 1, monthWeek = Math.ceil(monthDate / 7), days2 = (_a6 = recurrence.days) == null ? void 0 : _a6.slice(), months = (_b2 = recurrence.months) == null ? void 0 : _b2.slice(), monthDays = (_c2 = recurrence.monthDays) == null ? void 0 : _c2.slice(), positions = (_d = recurrence.positions) == null ? void 0 : _d.slice();
          recurrence.beginBatch();
          if (!((_e = me.getOccurrencesForDateRange(startDate, DateHelper.add(startDate, 1)).filter((r) => !(r.startDate - startDate))) == null ? void 0 : _e.length)) {
            if (days2 == null ? void 0 : days2.length) {
              ArrayHelper.remove(days2, RecurrenceDayRuleEncoder.encodeDay(oldStartDate.getDay()));
              days2.push(dayName2);
              recurrence.days = days2.sort();
            }
            if (monthDays == null ? void 0 : monthDays.length) {
              ArrayHelper.remove(monthDays, oldStartDate.getDate());
              monthDays.push(monthDate);
              recurrence.monthDays = monthDays.sort();
            }
            if (positions == null ? void 0 : positions.length) {
              ArrayHelper.remove(positions, oldMonthWeek);
              positions.push(monthWeek);
              recurrence.positions = [...new Set(positions)].sort();
            }
            if (months == null ? void 0 : months.length) {
              ArrayHelper.remove(months, oldStartDate.getMonth() + 1);
              months.push(monthNumber);
              recurrence.months = [...new Set(months)].sort();
            }
          }
          recurrence.endBatch();
        }
        if ("newExceptionDate" in wasSet) {
          me.meta.modified.exceptionDates = true;
          delete me.meta.modified.newExceptionDate;
          me.removeOccurrence(wasSet.newExceptionDate.value);
        }
        if (wasSet.recurrenceRule && !wasSet.recurrenceRule.value) {
          me.meta.modified.exceptionDates = true;
          me.exceptionDates = {};
        }
      }
      return super.afterChange(toSet, wasSet, silent, ...args);
    }
    /**
     * Detaches an occurrence from its owning recurring event so that it can be added to the eventStore
     * either as an exception, or as the start of a new recurring sequence.
     * @internal
     * @category Recurrence
     */
    detachFromRecurringEvent() {
      const me = this, { recurringTimeSpan, occurrenceDate, startDate } = me;
      me.recurringTimeSpan = null;
      recurringTimeSpan.addExceptionDate(occurrenceDate);
      if (me.recurrenceRule) {
        recurringTimeSpan.recurrence.endDate = DateHelper.add(startDate, -1, "minute");
      }
    }
    /**
     * The setter used by Model#inSet when {@link #function-addExceptionDate} is called.
     * Adding an exception must trigger change processing in a recurring event, so it must
     * be changed through a {@link Core.data.Model#function-set} call. Also, the change must be batchable
     * with other changes.
     * @private
     * @readonly
     * @category Recurrence
     */
    set newExceptionDate(date2) {
      if (date2) {
        const exceptionDates = this.exceptionDates || (this.exceptionDates = {});
        exceptionDates[DateHelper.makeKey(date2)] = 1;
      }
    }
    /**
     * Adds an exception date that should be skipped when generating occurrences for the timespan.
     * The method adds an entry to the array kept in {@link #field-exceptionDates} field.
     * @param {Date} date Exception date
     * @category Recurrence
     */
    addExceptionDate(newExceptionDate) {
      this.set({
        newExceptionDate
      });
    }
    /**
     * Does this recurring event have an exception on the passed date.
     * @param {Date} date The date to find an exception for.
     * @returns {Boolean} `true` if the event has an exception starting on the passed date.
     * @category Recurrence
     */
    hasException(date2) {
      var _a6;
      return Boolean((_a6 = this.exceptionDates) == null ? void 0 : _a6[DateHelper.makeKey(date2)]);
    }
    // Support TimeZonedDateMixin API
    convertToTimeZone(timeZone, ...args) {
      if (this.timeZone !== timeZone) {
        if (this.isRecurring) {
          this.removeOccurrences();
        }
      }
      super.convertToTimeZone(timeZone, ...args);
    }
  }, __publicField(_a5, "$name", "RecurringTimeSpan"), __publicField(_a5, "fields", [
    /**
     * The timespan recurrence rule. A string in [RFC-5545](https://tools.ietf.org/html/rfc5545#section-3.3.10)
     * described format ("RRULE" expression).
     * @field {String} recurrenceRule
     * @category Scheduling
     */
    {
      name: "recurrenceRule",
      internal: true
    },
    /**
     * A string (either a single date or multiple dates separated by comma) or an array of strings containing
     * the timespan exception dates. The dates that must be skipped when generating occurrences for a repeating
     * timespan. This is used to modify only individual occurrences of the timespan so the further regenerations
     * won't create another copy of this occurrence again.
     *
     * ```javascript
     * {
     *     id: 7,
     *     startDate: '2021-10-12T14:00:00',
     *     endDate: '2021-10-12T15:00:00',
     *     name: 'Lunch',
     *     resourceId: 'hotel',
     *     recurrenceRule: 'FREQ=DAILY;COUNT=5',
     *     exceptionDates: ['2021-10-14']
     * }
     * ```
     *
     * Use {@link #function-addExceptionDate} method to add an individual entry to the dates array:
     *
     * ```javascript
     * // Break the link between the occurrence and its base.
     * // This also adds the occurrence date as an exception date
     * // so that the base timespan knows that this date should be skipped when regenerating its occurrences.
     * occurrence.recurringTimeSpan = null;
     *
     * // now the occurrence is an individual record that can be changed & persisted freely
     * occurrence.setStartEndDate(new Date(2018, 6, 2), new Date(2018, 6, 3));
     * ```
     * **Note:** The dates in this field get automatically removed when the event changes its {@link Scheduler.model.TimeSpan#field-startDate start date}.
     *
     * @field {String|String[]} exceptionDates
     * @category Scheduling
     */
    {
      name: "exceptionDates",
      convert: convertExceptionDatesValue,
      serialize: serializeExceptionDatesValue,
      internal: true
    }
  ]), _a5;
};

// ../Scheduler/lib/Scheduler/model/mixin/EventModelMixin.js
var oneDayMS = 1e3 * 60 * 60 * 24;
var EventModelMixin_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    // Flag checked by EventStore to make sure it uses a valid subclass
    static get isEventModel() {
      return true;
    }
    /**
     * Set value for the specified field(s), triggering engine calculations immediately. See
     * {@link Core.data.Model#function-set Model#set()} for arguments.
     *
     * ```javascript
     * eventRecord.set('duration', 4);
     * // eventRecord.endDate is not yet calculated
     *
     * await eventRecord.setAsync('duration', 4);
     * // eventRecord.endDate is calculated
     * ```
     *
     * @param {String|Object} field The field to set value for, or an object with multiple values to set in one call
     * @param {*} [value] Value to set
     * @param {Boolean} [silent=false] Set to `true` to not trigger events. If event is recurring, occurrences won't be updated
     * automatically.
     * @function setAsync
     * @category Editing
     * @async
     */
    //region Fields
    static get fields() {
      return [
        /**
         * The start date of a time span (or Event / Task).
         *
         * Uses {@link Core/helper/DateHelper#property-defaultFormat-static DateHelper.defaultFormat} to convert a
         * supplied string to a Date. To specify another format, either change that setting or subclass TimeSpan and
         * change the dateFormat for this field.
         *
         * UI fields representing this data field are disabled for summary tasks. See {@link #function-isEditable}
         * for details.
         *
         * Note that the field always returns a `Date`.
         *
         * Also note that modifying the `startDate` at runtime will move the event in time, without affecting its
         * duration (with reservation for other scheduling logic affecting the duration). If you want to change the
         * `startDate` and `duration`, use {@link Scheduler/model/TimeSpan#function-setStartDate} instead (passing
         * `false` as the second argument).
         *
         * @field {Date} startDate
         * @accepts {String|Date}
         * @category Scheduling
         */
        /**
         * The end date of a time span (or Event / Task).
         *
         * Uses {@link Core/helper/DateHelper#property-defaultFormat-static DateHelper.defaultFormat} to convert a
         * supplied string to a Date. To specify another format, either change that setting or subclass TimeSpan and
         * change the dateFormat for this field.
         *
         * UI fields representing this data field are disabled for summary tasks. See {@link #function-isEditable} for details.
         *
         * Note that the field always returns a `Date`.
         *
         * @field {Date} endDate
         * @accepts {String|Date}
         * @category Scheduling
         */
        /**
         * The numeric part of the timespan's duration (the number of units).
         *
         * UI fields representing this data field are disabled for summary tasks. See {@link #function-isEditable}
         * for details.
         *
         * @field {Number} duration
         * @category Scheduling
         */
        /**
         * Calculated field which encapsulates the duration's magnitude and unit. This field will not be persisted,
         * setting it will update the {@link #field-duration} and
         * {@link Scheduler.model.TimeSpan#field-durationUnit} fields.
         *
         * UI fields representing this data field are disabled for summary tasks. See {@link #function-isEditable}
         * for details.
         *
         * @field {DurationConfig|Core.data.Duration} fullDuration
         * @category Scheduling
         */
        /**
         * The unique identifier of a task (mandatory)
         * @field {String|Number} id
         * @category Common
         */
        /**
         * Id of the resource this event is associated with (only usable for single assignments). We recommend
         * using assignments in an AssignmentStore over this approach. Internally any Event using `resourceId`
         * will have an assignment in AssignmentStore generated.
         * @field {String|Number} resourceId
         * @category Common
         */
        {
          name: "resourceId",
          internal: true
        },
        /**
         * Ids of the resources this event is associated with (can be used for for multiple assignments).
         * Any event using `resourceIds` will have assignments in AssignmentStore generated automatically.
         * It only applies if is configured with `persist: true`.
         * ```javascript
         *   class CustomEventModel extends EventModel {
         *       static $name = 'CustomEventModel';
         *
         *       static get fields() {
         *           return [
         *               { name : 'resourceIds', persist : true }
         *           ];
         *       }
         *   };
         *
         *   const
         *       resources   = [
         *           { id : 'r1', name : 'Celia' },
         *           { id : 'r2', name : 'Lee' },
         *           { id : 'r3', name : 'Macy' },
         *           { id : 'r4', name : 'Madison' }
         *       ],
         *       events      = [
         *           {
         *               id          : 1,
         *               resourceIds : ['r1', 'r2']
         *               ...
         *           },
         *           {
         *               id          : 2,
         *               resourceIds : ['r3', 'r4']
         *               ...
         *           }
         *       ];
         *
         *   const scheduler = new Scheduler({
         *       ...
         *       eventStore : {
         *           modelClass : CustomEventModel,
         *           data       : events
         *       },
         *       ...
         *   });
         * ```
         * @field {String[]|Number[]} resourceIds
         * @category Common
         */
        {
          name: "resourceIds",
          type: "array",
          persist: false,
          internal: true
        },
        /**
         * The array of {@link Scheduler.model.ResourceModel resources} which are assigned to this event.
         * @field {Scheduler.model.ResourceModel[]} resources
         * @category Common
         */
        {
          name: "resources",
          column: {
            type: "resourceassignment"
          },
          persist: false,
          internal: true,
          useProp: true
        },
        /**
         * Specify false to prevent the event from being dragged (if EventDrag feature is used)
         * @field {Boolean} draggable
         * @default true
         * @category Interaction
         */
        {
          name: "draggable",
          type: "boolean",
          persist: false,
          defaultValue: true,
          internal: true
        },
        /**
         * Specify `false` to prevent the event from being resized (if EventResize feature is used). You can also
         * specify `'start'` or `'end'` to only allow resizing in one direction
         * @field {Boolean|String} resizable
         * @default true
         * @category Interaction
         */
        {
          name: "resizable",
          persist: false,
          defaultValue: true,
          internal: true
        },
        // true, false, 'start' or 'end'
        /**
         * A field marking event as all day(s) spanning event.
         * For example, a holiday day may be represented by a `startDate`, and the `allDay` flag.
         * @field {Boolean} allDay
         * @category Scheduling
         */
        {
          name: "allDay",
          type: "boolean",
          defaultValue: false
        },
        /**
         * Controls this events appearance, see Schedulers
         * {@link Scheduler.view.mixin.TimelineEventRendering#config-eventStyle eventStyle config} for
         * available options.
         * @field {'plain'|'border'|'colored'|'hollow'|'line'|'dashed'|'minimal'|'rounded'|'calendar'|'interday'|null} eventStyle
         * @category Styling
         */
        {
          name: "eventStyle",
          internal: true
        },
        /**
         * Controls the primary color of the event. For available standard colors, see
         * {@link #typedef-EventColor}.
         * @field {EventColor|String|null} eventColor
         * @category Styling
         */
        {
          name: "eventColor",
          internal: true
        },
        /**
         * Width (in px) to use for this milestone when using Scheduler#milestoneLayoutMode 'data'.
         * @field {Number} milestoneWidth
         * @category Styling
         */
        {
          name: "milestoneWidth",
          internal: true
        },
        /**
         * Set this field to `false` to opt out of {@link Scheduler.feature.StickyEvents sticky event content}
         * (keeping event text in view while scrolling).
         * @field {Boolean} stickyContents
         * @category Styling
         */
        {
          name: "stickyContents",
          internal: true
        }
      ];
    }
    //endregion
    //region Id change
    updateAssignmentEventIds() {
      this.assigned.forEach((assignment) => {
        assignment.eventId = this.id;
      });
    }
    syncId(value2) {
      super.syncId(value2);
      this.updateAssignmentEventIds();
    }
    //endregion
    // This method is used by the `autoUpdateRecord` mode of the resource assignment field
    // here we route the resources update to the correct setter
    setValue(fieldName, value2) {
      var _a6;
      if (fieldName === "resource" && !this.usesSingleAssignment) {
        this.resources = (_a6 = ArrayHelper.asArray(value2)) != null ? _a6 : [];
      } else {
        return super.setValue(fieldName, value2);
      }
    }
    //region Resources
    /**
     * Returns all resources assigned to an event.
     *
     * @property {Scheduler.model.ResourceModel[]}
     * @category Assignments & Resources
     * @readonly
     */
    get resources() {
      return this.assignments.reduce((resources, { resource }) => {
        resource && resources.push(resource.$original);
        return resources;
      }, []);
    }
    set resources(resources) {
      var _a6;
      resources = ArrayHelper.asArray(resources);
      const me = this, newResourceIds = resources.map(me.constructor.asId);
      if (me.usesSingleAssignment) {
        me.set("resourceId", newResourceIds[0]);
      } else if ((_a6 = me.fieldMap) == null ? void 0 : _a6.resourceIds.persist) {
        me.resourceIds = newResourceIds;
      } else {
        const existingResourceIds = me.assignments.map((a) => a.resource.id), { onlyInA: toAdd, onlyInB: toRemove } = ArrayHelper.delta(newResourceIds, existingResourceIds);
        me.assignmentStore.add(toAdd.map((resourceId) => ({ resource: resourceId, event: me })));
        me.assignmentStore.remove(toRemove.map((resourceId) => me.assignments.find((a) => a.resource.id === resourceId)));
      }
    }
    /**
     * Iterate over all associated resources
     * @private
     * @category Assignments & Resources
     */
    forEachResource(fn2, thisObj = this) {
      for (const resource of this.resources) {
        if (fn2.call(thisObj, resource) === false) {
          return;
        }
      }
    }
    /**
     * Returns either the resource associated with this event (when called w/o `resourceId`) or resource
     * with specified id.
     *
     * @param {String} [resourceId] To retrieve a specific resource
     * @returns {Scheduler.model.ResourceModel}
     * @category Assignments & Resources
     */
    getResource(resourceId) {
      if (resourceId == null) {
        return this.resource;
      }
      return this.resourceStore ? this.resourceStore.getById(resourceId) : null;
    }
    //endregion
    //region Dates
    get startDate() {
      var _a6;
      let dt;
      if (this.isOccurrence) {
        dt = this.get("startDate");
      } else {
        dt = (_a6 = this._startDate) != null ? _a6 : super.startDate;
      }
      if (this.allDay) {
        dt = this.constructor.getAllDayStartDate(dt);
      }
      return dt;
    }
    set startDate(startDate) {
      if (this.batching) {
        this._startDate = startDate;
        this.set({ startDate });
      } else {
        super.startDate = startDate;
      }
    }
    get endDate() {
      var _a6;
      let dt;
      if (this.isOccurrence) {
        dt = this.get("endDate");
      } else {
        dt = (_a6 = this._endDate) != null ? _a6 : super.endDate;
      }
      if (this.allDay) {
        dt = this.constructor.getAllDayEndDate(dt);
      }
      return dt;
    }
    set endDate(endDate) {
      if (this.batching) {
        this._endDate = endDate;
        this.set({ endDate });
      } else {
        super.endDate = endDate;
      }
    }
    // Cannot use `convert` method because it might be disabled by `useRawData : true` and we always need to calculate
    // that value
    get wrapStartDate() {
      return this.startDate;
    }
    set wrapStartDate(value2) {
    }
    get wrapEndDate() {
      return this.endDate;
    }
    set wrapEndDate(value2) {
    }
    /**
     * Shift the dates for the date range by the passed amount and unit
     * @param {Number} amount The amount to shift
     * @param {DurationUnitShort} unit The unit to shift by, see {@link Core.helper.DateHelper}
     * for more information on valid formats.
     * @returns {Promise} A promise which is resolved when shift calculations are done
     * @async
     * @method shift
     * @category Scheduling
     */
    //endregion
    //region Is
    // Used internally to differentiate between Event and ResourceTimeRange
    get isEvent() {
      return true;
    }
    /**
     * Returns true if event can be drag and dropped
     * @property {Boolean}
     * @category Editing
     */
    get isDraggable() {
      return !this.readOnly && this.draggable;
    }
    /**
     * Returns true if event can be resized, but can additionally return 'start' or 'end' indicating how this event can
     * be resized.
     *
     * Milestones and parent events (that are not manuallyScheduled) cannot be resized.
     *
     * @property {Boolean|String}
     * @readonly
     * @category Editing
     */
    get isResizable() {
      return !this.isMilestone && (!this.isParent || this.manuallyScheduled) && this.resizable;
    }
    /**
     * Returns false if the event is not persistable. By default it always is, override this getter if you need
     * custom logic.
     *
     * @property {Boolean}
     * @readonly
     * @category Editing
     */
    get isPersistable() {
      return super.isPersistable && !this.isCreating;
    }
    endBatch(silent, skipAccessors, triggerBeforeUpdate) {
      const me = this, wasPersistable = me.isPersistable;
      super.endBatch(silent, skipAccessors, triggerBeforeUpdate);
      if (me.isPersistable && !wasPersistable && !me.ignoreBag && me.assigned) {
        for (const assignment of me.assigned) {
          assignment.stores.forEach(
            (s) => s.updateModifiedBagForRecord(assignment)
          );
        }
      }
    }
    get isCreating() {
      return super.isCreating;
    }
    set isCreating(value2) {
      var _a6, _b2;
      super.isCreating = value2;
      (_a6 = this.assignmentStore) == null ? void 0 : _a6.suspendAutoCommit();
      this.assignments.forEach((record) => record.isCreating = value2);
      (_b2 = this.assignmentStore) == null ? void 0 : _b2.resumeAutoCommit();
    }
    //endregion
    //region Single assignment compatibility
    get usesSingleAssignment() {
      return !this.eventStore || this.eventStore.usesSingleAssignment;
    }
    copy(...args) {
      const copy = super.copy(...args);
      if (!this.usesSingleAssignment) {
        copy.resourceId = null;
      }
      return copy;
    }
    /**
     * Override persistable getter to prevent sending resourceId when using multiple resource assignment mode
     * https://github.com/bryntum/support/issues/1345
     * @private
     */
    get persistableData() {
      const data = super.persistableData;
      if (!this.usesSingleAssignment) {
        delete data.resourceId;
      }
      return data;
    }
    /**
     * Returns the first assigned resource, or assigns a resource
     * @member {Scheduler.model.ResourceModel} resource
     * @category Assignments & Resources
     */
    get resource() {
      const { resources } = this;
      return resources.length ? resources[0] : null;
    }
    set resource(resourceRecord) {
      this.resourceId = this.constructor.asId(resourceRecord);
    }
    get resourceId() {
      var _a6;
      return this.usesSingleAssignment ? this.get("resourceId") : (_a6 = this.assignments[0]) == null ? void 0 : _a6.resourceId;
    }
    set resourceId(resourceId) {
      this.applyResourceId(resourceId);
    }
    get resourceIds() {
      var _a6, _b2, _c2;
      if ((_a6 = this.fieldMap) == null ? void 0 : _a6.resourceIds.persist) {
        return this.get("resourceIds");
      } else {
        return (_c2 = (_b2 = this.resources) == null ? void 0 : _b2.map((r) => r.id)) != null ? _c2 : [];
      }
    }
    set resourceIds(ids) {
      this.set("resourceIds", ids);
    }
    // Resources + any links to any of them
    get $linkedResources() {
      var _a6, _b2;
      return (_b2 = (_a6 = this.resources) == null ? void 0 : _a6.flatMap((resourceRecord) => [
        resourceRecord,
        ...resourceRecord.$links
      ])) != null ? _b2 : [];
    }
    applyResourceId(resourceId, fromApplyValue = false) {
      var _a6, _b2;
      const me = this, { eventStore, assignments } = me;
      if (me.isApplyingResourceId) {
        me.setData("resourceId", resourceId);
        return;
      }
      me.isApplyingResourceId = true;
      if (eventStore) {
        const assignmentStore = eventStore.assignmentStore || ((_a6 = eventStore.crudManager) == null ? void 0 : _a6.assignmentStore);
        if (resourceId != null) {
          if (!me.meta.skipEnforcingSingleAssignment) {
            eventStore.usesSingleAssignment = true;
          }
          if ((assignments == null ? void 0 : assignments.length) && resourceId !== assignments[0].resourceId) {
            const eventsSuspended = Boolean(eventStore.eventsSuspended);
            eventsSuspended && assignmentStore.suspendEvents();
            assignments[0].resource = resourceId;
            eventsSuspended && assignmentStore.resumeEvents();
          } else {
            assignmentStore.assignEventToResource(me, resourceId);
          }
        } else if (me.usesSingleAssignment || ((_b2 = me.resourceIds) == null ? void 0 : _b2.length)) {
          assignmentStore.remove(assignments);
        }
      } else if (!fromApplyValue) {
        me.set({ resourceId });
      }
      me.isApplyingResourceId = false;
    }
    applyResourceIds(resourceIds, fromApplyValue = false) {
      var _a6;
      const me = this, { eventStore, assignments } = me;
      if (me.fieldMap["resourceIds"].persist === false) {
        return false;
      }
      resourceIds = [...new Set(resourceIds)];
      if (eventStore) {
        const assignmentStore = eventStore.assignmentStore || ((_a6 = eventStore.crudManager) == null ? void 0 : _a6.assignmentStore);
        if (resourceIds == null ? void 0 : resourceIds.length) {
          if (assignments == null ? void 0 : assignments.length) {
            const eventsSuspended = Boolean(eventStore.eventsSuspended);
            eventsSuspended && assignmentStore.suspendEvents();
            assignments.forEach((assignment) => {
              const resourceIdToUpdate = resourceIds.find((resourceId) => !assignments.some((a) => a.resourceId === resourceId));
              if (resourceIdToUpdate) {
                assignment.resource = resourceIdToUpdate;
              }
            });
            const { onlyInA: toAdd, onlyInB: toRemove } = ArrayHelper.delta(resourceIds, assignments.map((assignment) => assignment.resourceId));
            assignmentStore.add(toAdd.map((resourceId) => ({ resource: resourceId, event: me })));
            assignmentStore.remove(toRemove.map((resourceId) => assignments.find((a) => a.resource.id === resourceId)));
            eventsSuspended && assignmentStore.resumeEvents();
          } else {
            assignmentStore.add(resourceIds.map((resourceId) => ({ resource: resourceId, event: me })));
          }
        } else {
          assignmentStore.remove(assignments);
        }
      } else if (!fromApplyValue) {
        me.set({ resourceIds });
      }
    }
    // Special handling of setting resourceId and resourceIds, creates assignment
    applyValue(useProp, mapping, value2, skipAccessors, field) {
      if (field && !this.meta.isAssigning) {
        const { eventStore } = this;
        switch (field.name) {
          case "resourceId":
            eventStore && (eventStore.isAssigning = true);
            this.applyResourceId(value2, true);
            break;
          case "resourceIds":
            eventStore && (eventStore.isAssigning = true);
            this.applyResourceIds(value2, true);
            break;
        }
        eventStore && (eventStore.isAssigning = false);
      }
      super.applyValue(useProp, mapping, value2, skipAccessors, field);
    }
    //endregion
    //region Assignment
    /**
     * Returns all assignments for the event. Event must be part of the store for this method to work.
     * @property {Scheduler.model.AssignmentModel[]}
     * @readonly
     * @category Assignments & Resources
     */
    get assignments() {
      return [...this.assigned || []];
    }
    /**
     * Assigns this event to the specified resource.
     *
     * *Note:* The event must be part of an EventStore for this to work. If the EventStore uses single assignment
     * (loaded using resourceId) existing assignments will always be removed.
     *
     * @param {Scheduler.model.ResourceModel|String|Number|Scheduler.model.ResourceModel[]|String[]|Number[]} resource A new resource for this event, either as a full
     *        Resource record or an id (or an array of such).
     * @param {Boolean} [removeExistingAssignments] `true` to first remove existing assignments
     * @typings removeExistingAssignments -> {Boolean|Number}
     * @category Assignments & Resources
     * @typings async
     */
    assign(resource, removeExistingAssignments = false) {
      const { eventStore } = this;
      if (eventStore && !eventStore.usesSingleAssignment) {
        return eventStore.assignEventToResource(this, resource, removeExistingAssignments);
      } else {
        if (Array.isArray(resource)) {
          this.resourceId = this.constructor.asId(resource[0]);
        } else {
          this.resourceId = this.constructor.asId(resource);
        }
        if (!eventStore) {
          this.meta.skipEnforcingSingleAssignment = true;
        }
      }
    }
    /**
     * Unassigns this event from the specified resource
     *
     * @param {Scheduler.model.ResourceModel|String|Number} [resource] The resource to unassign from.
     * @category Assignments & Resources
     * @typings async
     */
    unassign(resource, removingResource = false) {
      var _a6;
      const me = this;
      resource = me.constructor.asId(resource);
      me.meta.removingResource = removingResource;
      (_a6 = me.eventStore) == null ? void 0 : _a6.unassignEventFromResource(me, resource);
      me.meta.removingResource = null;
    }
    /**
     * Reassigns an event from an old resource to a new resource
     *
     * @param {Scheduler.model.ResourceModel|String|Number} oldResourceId A resource to unassign from or its id
     * @param {Scheduler.model.ResourceModel|String|Number} newResourceId A resource to assign to or its id
     * @category Assignments & Resources
     */
    reassign(oldResourceId, newResourceId) {
      this.eventStore && this.eventStore.reassignEventFromResourceToResource(this, oldResourceId, newResourceId);
    }
    /**
     * Returns true if this event is assigned to a certain resource.
     *
     * @param {Scheduler.model.ResourceModel|String|Number} resource The resource to query for
     * @returns {Boolean}
     * @category Assignments & Resources
     */
    isAssignedTo(resource) {
      const resourceId = this.constructor.asId(resource);
      return this.assignments.some((assignment) => assignment.resourceId === resourceId);
    }
    //endregion
    //region Dependencies
    /**
     * Returns all predecessor dependencies of this event
     *
     * @readonly
     * @property {Scheduler.model.DependencyBaseModel[]}
     * @category Dependencies
     */
    get predecessors() {
      var _a6;
      return [...(_a6 = this.incomingDeps) != null ? _a6 : []];
    }
    /**
     * Returns all successor dependencies of this event
     *
     * @readonly
     * @property {Scheduler.model.DependencyBaseModel[]}
     * @category Dependencies
     *
     */
    get successors() {
      var _a6;
      return [...(_a6 = this.outgoingDeps) != null ? _a6 : []];
    }
    get dependencies() {
      var _a6, _b2;
      return [...(_a6 = this.incomingDeps) != null ? _a6 : [], ...(_b2 = this.outgoingDeps) != null ? _b2 : []];
    }
    //endregion
    normalize() {
    }
    inSetNormalize() {
    }
    /**
     * The "main" event this model is an occurrence of.
     * Returns `null` for non-occurrences.
     * @property {Scheduler.model.EventModel}
     * @alias #Scheduler.model.mixin.RecurringTimeSpan#property-recurringTimeSpan
     * @readonly
     * @category Scheduling
     */
    get recurringEvent() {
      return this.recurringTimeSpan;
    }
    /**
     * Flag which indicates that this event is an interday event. This means that it spans
     * an entire day or multiple days.
     *
     * This is essentially used by the Calendar package to determine if an event should
     * go into the all day zone of a DayView.
     *
     * @property {Boolean}
     * @readonly
     * @category Scheduling
     */
    get isInterDay() {
      const { durationMS } = this;
      if (durationMS >= oneDayMS || !durationMS && this.allDay) {
        return true;
      }
      const {
        endDate,
        startDate
      } = this, eventStartMidnight = DateHelper.clearTime(startDate);
      if (startDate && endDate) {
        eventStartMidnight.setDate(eventStartMidnight.getDate() + 1);
        return (endDate || DateHelper.add(startDate, durationMS)) > eventStartMidnight;
      }
    }
    //region All day statics
    static getAllDayStartDate(dt) {
      if (dt && dt.isEvent) {
        dt = dt.get("startDate");
      }
      if (dt) {
        dt = DateHelper.clearTime(dt, true);
      }
      return dt;
    }
    static getAllDayEndDate(dt) {
      if (dt && dt.isEvent) {
        dt = dt.get("endDate");
      }
      if (dt && (dt.getHours() > 0 || dt.getMinutes() > 0 || dt.getSeconds() > 0 || dt.getMilliseconds() > 0)) {
        dt = DateHelper.getNext(dt, "d", 1);
      }
      return dt;
    }
    static getAllDayDisplayStartDate(dt) {
      if (dt && dt.isEvent) {
        dt = dt.get("startDate");
      }
      return DateHelper.clearTime(dt, true);
    }
    static getAllDayDisplayEndDate(startDate, endDate) {
      if (startDate && startDate.isEvent) {
        endDate = startDate.get("endDate");
        startDate = startDate.get("startDate");
      }
      if (endDate) {
        startDate = this.constructor.getAllDayDisplayStartDate(startDate);
        if (DateHelper.clearTime(endDate, true).valueOf() === endDate.valueOf()) {
          endDate = DateHelper.add(endDate, DateHelper.DAY, -1);
        } else if (startDate.valueOf() !== endDate.valueOf()) {
          endDate = DateHelper.clearTime(endDate, true);
        }
      }
      return endDate;
    }
    /**
     * Defines if the given event field should be manually editable in UI.
     * You can override this method to provide your own logic.
     *
     * By default, the method defines {@link #field-endDate}, {@link #field-duration} and {@link #field-fullDuration}
     * fields editable for leaf events only (in case the event is part of a tree store) and all other fields as
     * editable.
     *
     * @param {String} fieldName Name of the field
     * @returns {Boolean} Returns `true` if the field is editable, `false` if it is not and `undefined` if the event has
     * no such field.
     * @category Editing
     */
    isEditable(fieldName) {
      let editable = true;
      switch (fieldName) {
        case "endDate":
        case "duration":
        case "fullDuration":
          editable = this.isLeaf;
      }
      return editable && super.isEditable(fieldName);
    }
    //endregion
  }, __publicField(_a5, "$name", "EventModelMixin"), _a5;
};

// ../Engine/lib/Engine/quark/model/scheduler_core/CoreEventMixin.js
var CoreEventMixin = class extends Mixin([CorePartOfProjectModelMixin], (base) => {
  const superProto2 = base.prototype;
  class CoreEventMixin2 extends base {
    constructor() {
      super(...arguments);
      this._startDate = null;
      this._endDate = null;
      this._duration = null;
    }
    // Proper engine defines these fields since they enter graph, thus we need them
    static get fields() {
      return [
        { name: "startDate", type: "date" },
        { name: "endDate", type: "date" },
        { name: "duration", type: "number" },
        { name: "durationUnit", type: "durationunit", defaultValue: "day" }
      ];
    }
    // Getters return current or proposed value
    get startDate() {
      var _a5, _b2;
      return (_b2 = (_a5 = this._startDate) != null ? _a5 : this.get("startDate")) != null ? _b2 : null;
    }
    get endDate() {
      var _a5, _b2;
      return (_b2 = (_a5 = this._endDate) != null ? _a5 : this.get("endDate")) != null ? _b2 : null;
    }
    get duration() {
      var _a5, _b2;
      return (_b2 = (_a5 = this._duration) != null ? _a5 : this.get("duration")) != null ? _b2 : null;
    }
    // Route all setting through applyXX (setStartDate, startDate = , set('startDate'), batching)
    set startDate(value2) {
      this.proposeStartDate(value2);
    }
    set endDate(value2) {
      this.proposeEndDate(value2);
    }
    set duration(value2) {
      this.proposeDuration(value2);
    }
    //region Edge case normalization
    inSet(field, value2, silent, fromRelationUpdate, skipAccessors) {
      const me = this;
      if (me.project && !me.project.isWritingData && typeof field !== "string" && !skipAccessors) {
        if ("startDate" in field && !("startDate" in me.$changed)) {
          me.$changed.startDate = true;
          me.invalidate();
        }
        if ("endDate" in field && !("endDate" in me.$changed)) {
          me.$changed.endDate = true;
          me.invalidate();
        }
      }
      return superProto2.inSet.call(me, field, value2, silent, fromRelationUpdate, skipAccessors);
    }
    //endregion
    //region StartDate
    getStartDate() {
      return this.startDate;
    }
    proposeStartDate(startDate, keepDuration = !("endDate" in this.$changed)) {
      var _a5;
      if (this.inSetting || (startDate == null ? void 0 : startDate.getTime()) !== ((_a5 = this.startDate) == null ? void 0 : _a5.getTime())) {
        this._startDate = startDate;
        this.propose({ startDate, keepDuration });
      }
    }
    async setStartDate(startDate, keepDuration = true) {
      var _a5;
      this.proposeStartDate(startDate, keepDuration);
      return (_a5 = this.project) == null ? void 0 : _a5.commitAsync();
    }
    //endregion
    //region EndDate
    getEndDate() {
      return this.endDate;
    }
    proposeEndDate(endDate, keepDuration = false) {
      var _a5;
      if (this.inSetting || (endDate == null ? void 0 : endDate.getTime()) !== ((_a5 = this.endDate) == null ? void 0 : _a5.getTime())) {
        this._endDate = endDate;
        this.propose({ endDate, keepDuration });
      }
    }
    async setEndDate(endDate, keepDuration = false) {
      var _a5;
      this.proposeEndDate(endDate, keepDuration);
      return (_a5 = this.project) == null ? void 0 : _a5.commitAsync();
    }
    //endregion
    //region Duration
    getDuration() {
      return this.duration;
    }
    proposeDuration(duration, unit, keepStart = true) {
      this._duration = duration;
      this.propose({ duration, keepStart });
      if (unit)
        this.propose({ durationUnit: unit });
    }
    async setDuration(duration, unit, keepStart = true) {
      var _a5;
      this.proposeDuration(duration, unit, keepStart);
      return (_a5 = this.project) == null ? void 0 : _a5.commitAsync();
    }
    getDurationUnit() {
      return this.durationUnit;
    }
    //endregion
    // When joining as part of inline data, store is available. If joining through load, it is passed
    joinProject() {
      var _a5, _b2;
      const me = this;
      const changed = me.$changed;
      const startDate = me.getCurrentOrProposed("startDate");
      const endDate = me.getCurrentOrProposed("endDate");
      const duration = me.getCurrentOrProposed("duration");
      if (startDate != null)
        changed.startDate = me._startDate = startDate;
      if (endDate != null)
        changed.endDate = me._endDate = endDate;
      if (duration != null)
        changed.duration = me._duration = duration;
      if (me.eventStore && !me.eventStore.isLoadingData) {
        const unresolved = (_a5 = me.assignmentStore) == null ? void 0 : _a5.storage.findItem("event", null);
        if (unresolved) {
          const cachedAssignments = (_b2 = me.assignmentStore) == null ? void 0 : _b2.storage.findItem("eventId", me.id);
          if (cachedAssignments) {
            for (const assignment of cachedAssignments) {
              assignment.setChanged("event", me);
            }
          } else {
            for (const assignment of unresolved) {
              if (assignment.getCurrentOrProposed("event") === me.id) {
                assignment.setChanged("event", me);
              }
            }
          }
        }
      }
      superProto2.joinProject.call(me);
    }
    // Mimic how proper engine applies values
    applyValue(useProp, key, value2, skipAccessors, field) {
      var _a5;
      if ((this.project || ((_a5 = this.recurringTimeSpan) == null ? void 0 : _a5.project)) && field) {
        const { name } = field;
        if (name === "startDate" || name == "duration" || name === "endDate") {
          useProp = true;
          this["_" + name] = value2;
        }
        if (skipAccessors) {
          useProp = false;
        }
      }
      superProto2.applyValue.call(this, useProp, key, value2, skipAccessors, field);
    }
    // Catch changes from batches etc. In which case it is sometimes expected for data to be available directly
    afterChange(toSet, wasSet, silent, fromRelationUpdate, skipAccessors) {
      if (!this.$isCalculating && !skipAccessors) {
        this.setData({
          startDate: this.getCurrentOrProposed("startDate"),
          endDate: this.getCurrentOrProposed("endDate"),
          duration: this.getCurrentOrProposed("duration"),
          durationUnit: this.getCurrentOrProposed("durationUnit")
        });
      }
      superProto2.afterChange.call(this, toSet, wasSet, silent, fromRelationUpdate, skipAccessors);
    }
    // Normalizes dates & duration
    calculateInvalidated() {
      var _a5, _b2;
      const me = this;
      const changed = me.$changed;
      const changedStart = "startDate" in changed;
      const changedEnd = "endDate" in changed;
      const changedDuration = "duration" in changed;
      const { startDate, endDate, duration, keepDuration, keepStart } = changed;
      let calculate = null;
      if (changedStart && !changedEnd && !changedDuration) {
        if (startDate === null) {
          changed.endDate = null;
        } else if (me.hasCurrentOrProposed("endDate") && startDate > me.getCurrentOrProposed("endDate") && !keepDuration) {
          changed.endDate = startDate;
          changed.duration = 0;
        } else if (me.hasCurrentOrProposed("duration") && (keepDuration || !me.hasCurrentOrProposed("endDate"))) {
          calculate = "endDate";
        } else if (me.hasCurrentOrProposed("endDate")) {
          calculate = "duration";
        }
      } else if (!changedStart && changedEnd && !changedDuration) {
        if (endDate === null) {
          changed.startDate = null;
        } else if (me.hasCurrentOrProposed("startDate") && !keepDuration && endDate !== true && endDate < me.getCurrentOrProposed("startDate")) {
          changed.startDate = endDate;
          changed.duration = 0;
        } else if (me.hasCurrentOrProposed("duration") && (keepDuration || !me.hasCurrentOrProposed("startDate"))) {
          calculate = "startDate";
        } else if (me.hasCurrentOrProposed("startDate")) {
          calculate = "duration";
        }
      } else if (!changedStart && !changedEnd && changedDuration) {
        if (duration === null) {
          changed.endDate = null;
        } else if (me.hasCurrentOrProposed("startDate") && (keepStart || !me.hasCurrentOrProposed("endDate"))) {
          if (keepStart && changed.duration < 0) {
            changed.duration = 0;
          }
          calculate = "endDate";
        } else if (me.hasCurrentOrProposed("endDate")) {
          calculate = "startDate";
        }
      } else if (changedStart && changedEnd && !changedDuration) {
        if (startDate === null && endDate === null) {
          changed.duration = null;
        } else {
          calculate = "duration";
        }
      } else if (changedStart && !changedEnd && changedDuration) {
        calculate = "endDate";
      } else if (!changedStart && changedEnd && changedDuration) {
        calculate = "startDate";
      } else if (changedStart && changedEnd && changedDuration) {
        if (duration == null) {
          calculate = "duration";
        } else if (startDate == null) {
          calculate = "startDate";
        } else {
          calculate = "endDate";
        }
      }
      const currentOrProposedStartDate = me.getCurrentOrProposed("startDate");
      const currentOrProposedEndDate = me.getCurrentOrProposed("endDate");
      const currentOrProposedDuration = me.getCurrentOrProposed("duration");
      const currentOrProposedDurationUnit = me.getCurrentOrProposed("durationUnit");
      const adjustDurationToDST = (_b2 = (_a5 = me.getProject()) == null ? void 0 : _a5.adjustDurationToDST) != null ? _b2 : false;
      switch (calculate) {
        case "startDate":
          const newStartDate = DateHelper.add(currentOrProposedEndDate, -currentOrProposedDuration, currentOrProposedDurationUnit);
          if (adjustDurationToDST) {
            const dstDiff = currentOrProposedEndDate.getTimezoneOffset() - newStartDate.getTimezoneOffset();
            newStartDate.setTime(newStartDate.getTime() - dstDiff * 60 * 1e3);
          }
          changed.startDate = newStartDate;
          break;
        case "endDate":
          const newEndDate = DateHelper.add(currentOrProposedStartDate, currentOrProposedDuration, currentOrProposedDurationUnit);
          if (adjustDurationToDST) {
            const dstDiff = currentOrProposedStartDate.getTimezoneOffset() - newEndDate.getTimezoneOffset();
            newEndDate.setTime(newEndDate.getTime() - dstDiff * 60 * 1e3);
          }
          changed.endDate = newEndDate;
          break;
        case "duration":
          let newDuration = DateHelper.diff(currentOrProposedStartDate, currentOrProposedEndDate, "millisecond");
          if (adjustDurationToDST) {
            const dstDiff = currentOrProposedStartDate.getTimezoneOffset() - currentOrProposedEndDate.getTimezoneOffset();
            newDuration += dstDiff * 60 * 1e3;
          }
          changed.duration = DateHelper.as(currentOrProposedDurationUnit, newDuration, "millisecond");
          break;
      }
      if ("startDate" in changed && changed.startDate !== true)
        this._startDate = changed.startDate;
      if ("endDate" in changed && changed.endDate !== true)
        this._endDate = changed.endDate;
      if ("duration" in changed && changed.duration !== true)
        this._duration = changed.duration;
      if (changed.startDate === true)
        delete changed.startDate;
      if (changed.endDate === true)
        delete changed.endDate;
      delete changed.keepDuration;
      delete changed.keepStart;
    }
  }
  return CoreEventMixin2;
}) {
};

// ../Engine/lib/Engine/quark/model/AbstractHasAssignmentsMixin.js
var AbstractHasAssignmentsMixin = class extends Mixin([AbstractPartOfProjectModelMixin], (base) => {
  const superProto2 = base.prototype;
  class HasAssignmentsMixin extends base {
    /**
     * If a given resource is assigned to this task, returns a [[BaseAssignmentMixin]] instance for it.
     * Otherwise returns `null`
     */
    getAssignmentFor(resource) {
      var _a5;
      for (const assignment of (_a5 = this.assigned) != null ? _a5 : []) {
        if (assignment.resource === resource)
          return assignment;
      }
      return null;
    }
    isAssignedTo(resource) {
      return Boolean(this.getAssignmentFor(resource));
    }
    /**
     * A method which assigns a resource to the current event
     */
    async assign(resource) {
      const assignmentCls = this.project.assignmentStore.modelClass;
      this.addAssignment(new assignmentCls({
        event: this,
        resource
      }));
      return this.commitAsync();
    }
    /**
     * A method which unassigns a resource from the current event
     */
    async unassign(resource) {
      const assignment = this.getAssignmentFor(resource);
      this.removeAssignment(assignment);
      return this.commitAsync();
    }
    leaveProject() {
      if (this.isInActiveTransaction && this.assigned) {
        const eventStore = this.getEventStore();
        this.assigned.forEach((assignment) => eventStore.assignmentsForRemoval.add(assignment));
      }
      superProto2.leaveProject.call(this, ...arguments);
    }
    remove() {
      if (this.parent) {
        const eventStore = this.getEventStore();
        superProto2.remove.call(this);
        eventStore && eventStore.afterEventRemoval();
      } else {
        return superProto2.remove.call(this);
      }
    }
    // template methods, overridden in scheduling modes mixins
    // should probably be named something like "onEventAssignmentAdded"
    // should be a listener for the `add` event of the assignment store instead
    addAssignment(assignment) {
      this.getProject().assignmentStore.add(assignment);
      return assignment;
    }
    // should be a listener for the `remove` event of the assignment store instead
    removeAssignment(assignment) {
      this.getProject().assignmentStore.remove(assignment);
      return assignment;
    }
  }
  return HasAssignmentsMixin;
}) {
};

// ../Engine/lib/Engine/quark/model/scheduler_core/CoreHasAssignmentsMixin.js
var CoreHasAssignmentsMixin = class extends Mixin([CoreEventMixin, AbstractHasAssignmentsMixin], (base) => {
  const superProto2 = base.prototype;
  class CoreHasAssignmentsMixin2 extends base {
    get assigned() {
      var _a5, _b2;
      return (_b2 = (_a5 = this.project) == null ? void 0 : _a5.assignmentStore.getEventsAssignments(this)) != null ? _b2 : this.$cachedAssignments;
    }
    leaveProject(isReplacing = false) {
      this.$cachedAssignments = this.assigned;
      super.leaveProject(isReplacing);
    }
    applyValue(useProp, key, value2, skipAccessor, field) {
      var _a5;
      if (key === "id") {
        (_a5 = this.assigned) == null ? void 0 : _a5.forEach((assignment) => assignment.set("eventId", value2));
      }
      superProto2.applyValue.call(this, useProp, key, value2, skipAccessor, field);
    }
    copy(newId = null, deep = null) {
      const copy = superProto2.copy.call(this, newId, deep);
      if (ObjectHelper.isObject(deep) && !deep.skipFieldIdentifiers || !ObjectHelper.isObject(deep)) {
        copy.$cachedAssignments = this.assigned;
      }
      return copy;
    }
  }
  return CoreHasAssignmentsMixin2;
}) {
};

// ../Engine/lib/Engine/quark/model/scheduler_core/CoreHasDependenciesMixin.js
var CoreHasDependenciesMixin = class extends Mixin([CoreEventMixin], (base) => {
  const superProto2 = base.prototype;
  class CoreHasDependenciesMixin2 extends base {
    get outgoingDeps() {
      return this.project.dependencyStore.getOutgoingDepsForEvent(this);
    }
    get incomingDeps() {
      return this.project.dependencyStore.getIncomingDepsForEvent(this);
    }
    leaveProject() {
      const eventStore = this.eventStore;
      if (this.outgoingDeps) {
        this.outgoingDeps.forEach((dependency) => eventStore.dependenciesForRemoval.add(dependency));
      }
      if (this.incomingDeps) {
        this.incomingDeps.forEach((dependency) => eventStore.dependenciesForRemoval.add(dependency));
      }
      superProto2.leaveProject.call(this);
    }
  }
  return CoreHasDependenciesMixin2;
}) {
};

// ../Engine/lib/Engine/quark/model/scheduler_core/SchedulerCoreEvent.js
var SchedulerCoreEvent = class extends Mixin([
  CoreEventMixin,
  CoreHasAssignmentsMixin,
  CoreHasDependenciesMixin
], (base) => {
  const superProto2 = base.prototype;
  class SchedulerCoreEvent2 extends base {
  }
  return SchedulerCoreEvent2;
}) {
};

// ../Scheduler/lib/Scheduler/model/EventModel.js
var EngineMixin3 = SchedulerCoreEvent;
var EventModel = class extends EngineMixin3.derive(TimeSpan).mixin(
  RecurringTimeSpan_default,
  PartOfProject_default,
  EventModelMixin_default
) {
};
__publicField(EventModel, "$name", "EventModel");
EventModel.exposeProperties();
EventModel._$name = "EventModel";

// ../Engine/lib/Engine/quark/store/AbstractEventStoreMixin.js
var dataAddRemoveActions3 = {
  splice: 1,
  clear: 1
};
var AbstractEventStoreMixin = class extends Mixin([AbstractPartOfProjectStoreMixin], (base) => {
  const superProto2 = base.prototype;
  class AbstractEventStoreMixin2 extends base {
    constructor() {
      super(...arguments);
      this.assignmentsForRemoval = /* @__PURE__ */ new Set();
      this.dependenciesForRemoval = /* @__PURE__ */ new Set();
    }
    // we need `onDataChange` for `syncDataOnLoad` option to work
    onDataChange(event) {
      var _a5;
      const isAddRemove = dataAddRemoveActions3[event.action];
      super.onDataChange(event);
      if (isAddRemove && ((_a5 = event.removed) == null ? void 0 : _a5.length))
        this.afterEventRemoval();
    }
    // it seems `onDataChange` is not triggered for `remove` with `silent` flag
    remove(records, silent) {
      const res = superProto2.remove.call(this, records, silent);
      this.afterEventRemoval();
      return res;
    }
    // it seems `onDataChange` is not triggered for `TreeStore#removeAll()`
    removeAll(silent) {
      const res = superProto2.removeAll.call(this, silent);
      this.afterEventRemoval();
      return res;
    }
    clear(...args) {
      const res = superProto2.clear.apply(this, args);
      this.afterEventRemoval();
      return res;
    }
    onNodeRemoveChild(parent, children, index, flags) {
      const removed = superProto2.onNodeRemoveChild.call(this, ...arguments);
      this.afterEventRemoval();
      return removed;
    }
    afterEventRemoval() {
      const { assignmentsForRemoval, dependenciesForRemoval } = this;
      if (!assignmentsForRemoval)
        return;
      const assignmentStore = this.getAssignmentStore();
      if (assignmentStore && !assignmentStore.allAssignmentsForRemoval && assignmentsForRemoval.size) {
        const toRemove = [...assignmentsForRemoval].filter((assignment) => !assignmentStore.assignmentsForRemoval.has(assignment));
        toRemove.length > 0 && assignmentStore.remove(toRemove);
      }
      assignmentsForRemoval.clear();
      const dependencyStore = this.getDependencyStore();
      if (dependencyStore && !dependencyStore.allDependenciesForRemoval && dependenciesForRemoval.size) {
        const toRemove = [...dependenciesForRemoval].filter((dependency) => !dependencyStore.dependenciesForRemoval.has(dependency));
        toRemove.length > 0 && dependencyStore.remove(toRemove);
      }
      dependenciesForRemoval.clear();
    }
    processRecord(eventRecord, isDataset = false) {
      var _a5;
      if (!((_a5 = this.project) == null ? void 0 : _a5.isRepopulatingStores)) {
        const existingRecord = this.getById(eventRecord.id);
        const isReplacing = existingRecord && existingRecord !== eventRecord;
        if (isReplacing && existingRecord.assigned) {
          for (const assignment of existingRecord.assigned) {
            assignment.event = eventRecord;
          }
        }
      }
      return eventRecord;
    }
  }
  return AbstractEventStoreMixin2;
}) {
};

// ../Engine/lib/Engine/quark/store/CoreEventStoreMixin.js
var CoreEventStoreMixin = class extends Mixin([AbstractEventStoreMixin, CorePartOfProjectStoreMixin], (base) => {
  const superProto2 = base.prototype;
  class CoreEventStoreMixin2 extends base {
    static get defaultConfig() {
      return {
        modelClass: SchedulerCoreEvent
      };
    }
    joinProject() {
      var _a5;
      (_a5 = this.assignmentStore) == null ? void 0 : _a5.linkAssignments(this, "event");
    }
    afterLoadData() {
      var _a5;
      this.afterEventRemoval();
      (_a5 = this.assignmentStore) == null ? void 0 : _a5.linkAssignments(this, "event");
    }
  }
  return CoreEventStoreMixin2;
}) {
};

// ../Scheduler/lib/Scheduler/data/EventStore.js
var EngineMixin4 = PartOfProject_default(CoreEventStoreMixin.derive(AjaxStore));
var EventStore = class extends EngineMixin4.mixin(
  RecurringEventsMixin_default,
  EventStoreMixin_default,
  DayIndexMixin_default,
  GetEventsMixin_default
) {
  static get defaultConfig() {
    return {
      /**
       * Class used to represent records
       * @config {Scheduler.model.EventModel}
       * @typings {typeof EventModel}
       * @default
       * @category Common
       */
      modelClass: EventModel
    };
  }
};
__publicField(EventStore, "$name", "EventStore");
EventStore._$name = "EventStore";

// ../Scheduler/lib/Scheduler/model/mixin/AssignmentModelMixin.js
var getPath = ObjectHelper.getPath;
var setFieldValue = (data, field, complexMapping, value2) => {
  if (complexMapping) {
    ObjectHelper.setPath(data, field, value2);
  } else {
    data[field] = value2;
  }
};
var setModelField = (data, field, complexMapping, model) => {
  var _a5;
  if ((model == null ? void 0 : model.isModel) && !((_a5 = getPath(data, field)) == null ? void 0 : _a5.model)) {
    setFieldValue(data, field, complexMapping, model);
  }
};
var AssignmentModelMixin_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    /**
     * Set value for the specified field(s), triggering engine calculations immediately. See
     * {@link Core.data.Model#function-set Model#set()} for arguments.
     *
     * ```javascript
     * assignment.set('resourceId', 2);
     * // assignment.resource is not yet resolved
     *
     * await assignment.setAsync('resourceId', 2);
     * // assignment.resource is resolved
     * ```
     *
     * @param {String|Object} field The field to set value for, or an object with multiple values to set in one call
     * @param {*} [value] Value to set
     * @param {Boolean} [silent=false] Set to `true` to not trigger events
     * automatically.
     * @function setAsync
     * @category Editing
     * @async
     */
    //region Fields
    static get fields() {
      return [
        /**
         * Id for the resource to assign to
         * @field {String|Number} resourceId
         * @category Common
         */
        "resourceId",
        /**
         * Id for the event to assign
         * @field {String|Number} eventId
         * @category Common
         */
        "eventId",
        /**
         * Specify `false` to opt out of drawing dependencies from/to this assignment
         * @field {Boolean} drawDependencies
         * @category Common
         */
        { name: "drawDependencies", type: "boolean" },
        "event",
        "resource"
      ];
    }
    //endregion
    construct(data, ...args) {
      var _a6, _b2;
      data = data ? Object.isExtensible(data) ? data : Object.assign({}, data) : {};
      const { fieldMap } = this, eventIdField = fieldMap.eventId.dataSource, resourceIdField = fieldMap.resourceId.dataSource, eventField = fieldMap.event.dataSource, resourceField = fieldMap.resource.dataSource, eventIdComplexMapping = fieldMap.eventId.complexMapping, resourceIdComplexMapping = fieldMap.resourceId.complexMapping, eventComplexMapping = fieldMap.event.complexMapping, resourceComplexMapping = fieldMap.resource.complexMapping, eventId = eventIdComplexMapping ? getPath(data, eventIdField) : data[eventIdField], resourceId = resourceIdComplexMapping ? getPath(data, resourceIdField) : data[resourceIdField], event = ((_a6 = data.event) == null ? void 0 : _a6.isModel) ? data.event : eventComplexMapping ? getPath(data, eventField) : data[eventField], resource = ((_b2 = data.resource) == null ? void 0 : _b2.isModel) ? data.resource : resourceComplexMapping ? getPath(data, resourceField) : data[resourceField];
      if (eventId != null) {
        setFieldValue(data, eventField, eventComplexMapping, eventId);
      } else if (event != null) {
        const value2 = event.isModel ? event.id : event;
        setFieldValue(data, eventIdField, eventIdComplexMapping, value2);
      }
      if (resourceId != null) {
        setFieldValue(data, resourceField, resourceComplexMapping, resourceId);
      } else if (resource != null) {
        const value2 = resource.isModel ? resource.id : resource;
        setFieldValue(data, resourceIdField, resourceIdComplexMapping, value2);
      }
      setModelField(data, eventField, eventComplexMapping, data.event);
      setModelField(data, resourceField, resourceComplexMapping, data.resource);
      super.construct(data, ...args);
    }
    //region Event & resource
    /**
     * A key made up from the event id and the id of the resource assigned to.
     * @property eventResourceKey
     * @readonly
     * @internal
     */
    get eventResourceKey() {
      return this.buildEventResourceKey(this.event, this.resource);
    }
    buildEventResourceKey(event, resource) {
      let eventKey, resourceKey;
      if (event) {
        eventKey = event.isModel ? event.id : event;
      } else {
        eventKey = this.internalId;
      }
      if (resource) {
        resourceKey = resource.isModel ? resource.id : resource;
      } else {
        resourceKey = this.internalId;
      }
      return `${eventKey}-${resourceKey}`;
    }
    buildIndexKey({ event, resource }) {
      return this.buildEventResourceKey(event, resource);
    }
    set(field, value2, ...args) {
      var _a6, _b2;
      const toSet = this.fieldToKeys(field, value2);
      if ("resource" in toSet) {
        if (((_a6 = toSet.resource) == null ? void 0 : _a6.id) !== void 0) {
          toSet.resourceId = toSet.resource.id;
        }
      } else if ("resourceId" in toSet && this.constructor.isProAssignmentModel) {
        toSet.resource = toSet.resourceId;
      }
      if ("event" in toSet) {
        if (((_b2 = toSet.event) == null ? void 0 : _b2.id) !== void 0) {
          toSet.eventId = toSet.event.id;
        }
      } else if ("eventId" in toSet && this.constructor.isProAssignmentModel) {
        toSet.event = toSet.eventId;
      }
      return super.set(toSet, null, ...args);
    }
    afterChange(toSet, wasSet, silent, fromRelationUpdate, skipAccessors) {
      var _a6, _b2;
      const me = this;
      if (!me.constructor.isProAssignmentModel && (wasSet == null ? void 0 : wasSet.resourceId) && ((_a6 = me.resource) == null ? void 0 : _a6.id) !== wasSet.resourceId.value) {
        me.resource = wasSet.resourceId.value;
      } else if (me.constructor.isProAssignmentModel && ((_b2 = me.project) == null ? void 0 : _b2.propagatingSyncChanges) && (wasSet == null ? void 0 : wasSet.eventId) && !(wasSet == null ? void 0 : wasSet.event) && (toSet.event.value === wasSet.eventId.value || toSet.event.value.id === wasSet.eventId.value)) {
        delete wasSet.eventId;
        delete me.meta.modified.eventId;
      }
      return super.afterChange(...arguments);
    }
    // Settings resourceId relays to `resource`. Underlying data will be updated in `afterChange()` above
    set resourceId(value2) {
      const { resource } = this;
      if ((resource == null ? void 0 : resource.isModel) && resource.id === value2) {
        this.set("resourceId", value2);
      } else {
        this.resource = value2;
      }
    }
    get resourceId() {
      var _a6, _b2;
      return (_b2 = (_a6 = this.resource) == null ? void 0 : _a6.id) != null ? _b2 : this.get("resourceId");
    }
    // Same for event as for resourceId
    set eventId(value2) {
      const { event } = this;
      if ((event == null ? void 0 : event.isModel) && event.id === value2) {
        this.set("eventId", value2);
      } else {
        this.event = value2;
      }
    }
    get eventId() {
      var _a6, _b2;
      return (_b2 = (_a6 = this.event) == null ? void 0 : _a6.id) != null ? _b2 : this.get("eventId");
    }
    /**
     * Convenience property to get the name of the associated event.
     * @property {String}
     * @readonly
     */
    get eventName() {
      var _a6;
      return (_a6 = this.event) == null ? void 0 : _a6.name;
    }
    /**
     * Convenience property to get the name of the associated resource.
     * @property {String}
     * @readonly
     */
    get resourceName() {
      var _a6;
      return (_a6 = this.resource) == null ? void 0 : _a6.name;
    }
    /**
     * Returns the resource associated with this assignment.
     *
     * @returns {Scheduler.model.ResourceModel} Instance of resource
     */
    getResource() {
      return this.resource;
    }
    //endregion
    // Convenience getter to not have to check `instanceof AssignmentModel`
    get isAssignment() {
      return true;
    }
    /**
     * Returns `true` if the Assignment can be persisted (e.g. task and resource are not 'phantoms').
     *
     * Note that when using single assignments, assignments are not persisted.
     *
     * @property {Boolean}
     */
    get isPersistable() {
      const {
        event,
        resource,
        unjoinedStores,
        assignmentStore,
        eventStore
      } = this, crudManager = assignmentStore == null ? void 0 : assignmentStore.crudManager;
      let result;
      if (eventStore == null ? void 0 : eventStore.usesSingleAssignment) {
        return false;
      }
      if (assignmentStore) {
        result = this.isValid && !(event == null ? void 0 : event.isCreating) && (crudManager || !(event == null ? void 0 : event.isPhantom) && !(resource == null ? void 0 : resource.isPhantom));
      } else {
        result = !this.isPhantom && Boolean(unjoinedStores[0]);
      }
      return result && super.isPersistable && !(event == null ? void 0 : event.isCreating);
    }
    get isValid() {
      return this.resource != null && this.event != null;
    }
    /**
     * Returns a textual representation of this assignment (e.g. Mike 50%).
     * @returns {String}
     */
    toString() {
      if (this.resourceName) {
        return `${this.resourceName} ${Math.round(this.units)}%`;
      }
      return "";
    }
    //region STM hooks
    shouldRecordFieldChange(fieldName, oldValue, newValue) {
      var _a6, _b2;
      if (!super.shouldRecordFieldChange(fieldName, oldValue, newValue)) {
        return false;
      }
      if (fieldName === "event" || fieldName === "eventId") {
        const eventStore = (_a6 = this.project) == null ? void 0 : _a6.eventStore;
        if (eventStore && eventStore.oldIdMap[oldValue] === eventStore.getById(newValue)) {
          return false;
        }
      }
      if (fieldName === "resource" || fieldName === "resourceId") {
        const resourceStore = (_b2 = this.project) == null ? void 0 : _b2.resourceStore;
        if (resourceStore && resourceStore.oldIdMap[oldValue] === resourceStore.getById(newValue)) {
          return false;
        }
      }
      return true;
    }
    //endregion
  }, __publicField(_a5, "$name", "AssignmentModelMixin"), _a5;
};

// ../Engine/lib/Engine/quark/model/scheduler_core/CoreAssignmentMixin.js
function asId(recordOrId) {
  return (recordOrId == null ? void 0 : recordOrId.isModel) ? recordOrId.id : recordOrId;
}
var CoreAssignmentMixin = class extends Mixin([CorePartOfProjectModelMixin], (base) => {
  const superProto2 = base.prototype;
  class CoreAssignmentMixin2 extends base {
    // Fields declared in the Model way, existing decorators all assume ChronoGraph is used
    static get fields() {
      return [
        // isEqual required to properly detect changed resource / event
        { name: "resource", isEqual: (a, b) => a === b, persist: false },
        { name: "event", isEqual: (a, b) => a === b, persist: false }
      ];
    }
    // Resolve early + update indices to have buckets ready before commit
    setChanged(field, value2, invalidate) {
      const { assignmentStore, eventStore, resourceStore, project } = this;
      let update = false;
      if (field === "event") {
        const event = isInstanceOf(value2, CoreEventMixin) ? value2 : eventStore == null ? void 0 : eventStore.$master.getById(value2);
        if (event)
          update = true;
        value2 = event || value2;
      }
      if (field === "resource") {
        const resource = isInstanceOf(value2, CoreResourceMixin) ? value2 : resourceStore == null ? void 0 : resourceStore.$master.getById(value2);
        if (resource)
          update = true;
        value2 = resource || value2;
      }
      superProto2.setChanged.call(this, field, value2, invalidate, true);
      if (assignmentStore && update && !project.isPerformingCommit && !assignmentStore.isLoadingData && !(resourceStore == null ? void 0 : resourceStore.isLoadingData) && !assignmentStore.skipInvalidateIndices) {
        assignmentStore.invalidateIndices();
      }
    }
    // Resolve event and resource when joining project
    joinProject() {
      superProto2.joinProject.call(this);
      this.setChanged("event", this.get("event"));
      this.setChanged("resource", this.get("resource"));
    }
    // Resolved resource & event as part of commit
    // Normally done earlier in setChanged, but stores might not have been available yet at that point
    calculateInvalidated() {
      var _a5, _b2;
      let { event = this.event, resource = this.resource } = this.$changed;
      if (event !== null && !isInstanceOf(event, CoreEventMixin)) {
        const resolved = (_a5 = this.eventStore) == null ? void 0 : _a5.getById(event);
        if (resolved)
          this.setChanged("event", resolved, false);
      }
      if (resource !== null && !isInstanceOf(resource, CoreResourceMixin)) {
        const resolved = (_b2 = this.resourceStore) == null ? void 0 : _b2.getById(resource);
        if (resolved)
          this.setChanged("resource", resolved, false);
      }
    }
    // resourceId and eventId required to be available for new datasets
    finalizeInvalidated(silent) {
      const changed = this.$changed;
      if ("resource" in changed) {
        changed.resourceId = asId(changed.resource);
      }
      if ("event" in changed) {
        changed.eventId = asId(changed.event);
      }
      superProto2.finalizeInvalidated.call(this, silent);
    }
    //region Event
    set event(event) {
      this.setChanged("event", event);
      this.setChanged("eventId", asId(event));
    }
    get event() {
      const event = this.get("event");
      return (event == null ? void 0 : event.id) != null ? event : null;
    }
    //endregion
    //region Resource
    set resource(resource) {
      this.setChanged("resource", resource);
      this.setChanged("resourceId", asId(resource));
    }
    get resource() {
      const resource = this.get("resource");
      return (resource == null ? void 0 : resource.id) != null ? resource : null;
    }
  }
  return CoreAssignmentMixin2;
}) {
};

// ../Scheduler/lib/Scheduler/model/AssignmentModel.js
var EngineMixin5 = CoreAssignmentMixin;
var AssignmentModel = class extends AssignmentModelMixin_default(PartOfProject_default(EngineMixin5.derive(Model))) {
};
// NOTE: Leave field defs at top to be picked up by jsdoc
/**
 * Id for event to assign. Can be used as an alternative to `eventId`, but please note that after
 * load it will be populated with the actual event and not its id. This field is not persistable.
 * @field {Scheduler.model.EventModel} event
 * @accepts {String|Number|Scheduler.model.EventModel}
 * @typings {String|Number|Scheduler.model.EventModel|Scheduler.model.TimeSpan}
 * @category Common
 */
/**
 * Id for resource to assign to. Can be used as an alternative to `resourceId`, but please note that after
 * load it will be populated with the actual resource and not its id. This field is not persistable.
 * @field {Scheduler.model.ResourceModel} resource
 * @accepts {String|Number|Scheduler.model.ResourceModel}
 * @category Common
 */
__publicField(AssignmentModel, "$name", "AssignmentModel");
AssignmentModel.exposeProperties();
AssignmentModel._$name = "AssignmentModel";

// ../Scheduler/lib/Scheduler/data/mixin/AssignmentStoreMixin.js
var AssignmentStoreMixin_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    /**
     * Add assignments to the store.
     *
     * NOTE: References (event, resource) on the assignments are determined async by a calculation engine. Thus they
     * cannot be directly accessed after using this function.
     *
     * For example:
     *
     * ```javascript
     * const [assignment] = assignmentStore.add({ eventId, resourceId });
     * // assignment.event is not yet available
     * ```
     *
     * To guarantee references are set up, wait for calculations for finish:
     *
     * ```javascript
     * const [assignment] = assignmentStore.add({ eventId, resourceId });
     * await assignmentStore.project.commitAsync();
     * // assignment.event is available (assuming EventStore is loaded and so on)
     * ```
     *
     * Alternatively use `addAsync()` instead:
     *
     * ```javascript
     * const [assignment] = await assignmentStore.addAsync({ eventId, resourceId });
     * // assignment.event is available (assuming EventStore is loaded and so on)
     * ```
     *
     * @param {Scheduler.model.AssignmentModel|Scheduler.model.AssignmentModel[]|AssignmentModelConfig|AssignmentModelConfig[]} records
     * Array of records/data or a single record/data to add to store
     * @param {Boolean} [silent] Specify `true` to suppress events
     * @returns {Scheduler.model.AssignmentModel[]} Added records
     * @function add
     * @category CRUD
     */
    /**
     * Add assignments to the store and triggers calculations directly after. Await this function to have up to date
     * references on the added assignments.
     *
     * ```javascript
     * const [assignment] = await assignmentStore.addAsync({ eventId, resourceId });
     * // assignment.event is available (assuming EventStore is loaded and so on)
     * ```
     *
     * @param {Scheduler.model.AssignmentModel|Scheduler.model.AssignmentModel[]|AssignmentModelConfig|AssignmentModelConfig[]} records
     * Array of records/data or a single record/data to add to store
     * @param {Boolean} [silent] Specify `true` to suppress events
     * @returns {Scheduler.model.AssignmentModel[]} Added records
     * @function addAsync
     * @category CRUD
     * @async
     */
    /**
     * Applies a new dataset to the AssignmentStore. Use it to plug externally fetched data into the store.
     *
     * NOTE: References (assignments, resources) on the assignments are determined async by a calculation engine. Thus
     * they cannot be directly accessed after assigning the new dataset.
     *
     * For example:
     *
     * ```javascript
     * assignmentStore.data = [{ eventId, resourceId }];
     * // assignmentStore.first.event is not yet available
     * ```
     *
     * To guarantee references are available, wait for calculations for finish:
     *
     * ```javascript
     * assignmentStore.data = [{ eventId, resourceId  }];
     * await assignmentStore.project.commitAsync();
     * // assignmentStore.first.event is available
     * ```
     *
     * Alternatively use `loadDataAsync()` instead:
     *
     * ```javascript
     * await assignmentStore.loadDataAsync([{ eventId, resourceId }]);
     * // assignmentStore.first.event is available
     * ```
     *
     * @member {AssignmentModelConfig[]} data
     * @category Records
     */
    /**
     * Applies a new dataset to the AssignmentStore and triggers calculations directly after. Use it to plug externally
     * fetched data into the store.
     *
     * ```javascript
     * await assignmentStore.loadDataAsync([{ eventId, resourceId }]);
     * // assignmentStore.first.event is available
     * ```
     *
     * @param {AssignmentModelConfig[]} data Array of AssignmentModel data objects
     * @function loadDataAsync
     * @category CRUD
     * @async
     */
    static get defaultConfig() {
      return {
        /**
         * CrudManager must load stores in the correct order. Lowest first.
         * @private
         */
        loadPriority: 300,
        /**
         * CrudManager must sync stores in the correct order. Lowest first.
         * @private
         */
        syncPriority: 300,
        id: "assignments"
      };
    }
    get $lazyLoadPluginClass() {
      return this.isPaged ? PagedDateStoreLazyLoadPlugin : DateStoreLazyLoadPlugin;
    }
    add(newAssignments, ...args) {
      var _a6;
      newAssignments = ArrayHelper.asArray(newAssignments);
      if (!(newAssignments == null ? void 0 : newAssignments.length)) {
        return;
      }
      for (let i = 0; i < newAssignments.length; i++) {
        let assignment = newAssignments[i];
        if (!(assignment instanceof Model)) {
          newAssignments[i] = assignment = this.createRecord(assignment);
        }
        if (!this.isSyncingDataOnLoad && this.storage.findIndex("eventResourceKey", assignment.eventResourceKey, true) !== -1) {
          throw new Error(`Duplicate assignment Event: ${assignment.eventId} to resource: ${assignment.resourceId}`);
        }
        if ((_a6 = assignment.event) == null ? void 0 : _a6.isCreating) {
          assignment.isCreating = true;
        }
      }
      return super.add(newAssignments, ...args);
    }
    includesAssignment(eventId, resourceId) {
      return this.storage.findIndex("eventResourceKey", `${eventId}-${resourceId}`, true) !== -1;
    }
    setStoreData(data) {
      if (this.usesSingleAssignment) {
        throw new Error("Data loading into AssignmentStore (multi-assignment mode) cannot be combined with EventStore data containing resourceId (single-assignment mode)");
      }
      super.setStoreData(data);
    }
    //region Init & destroy
    // This index fixes poor performance when you add large number of events to an event store with large number of
    // events - if cache is missing existing records are iterated n² times.
    // https://github.com/bryntum/support/issues/3154#issuecomment-881336588
    set storage(storage) {
      super.storage = storage;
      this.storage.addIndex({
        property: "eventResourceKey",
        dependentOn: { event: true, resource: true },
        onDuplicate(assignment) {
          console.warn(`Duplicate assignment of event ${assignment.eventId} to resource ${assignment.resourceId}`);
        }
      });
    }
    get storage() {
      return this._storage || super.storage;
    }
    //endregion
    //region Stores
    // To not have to do instanceof checks
    get isAssignmentStore() {
      return true;
    }
    //endregion
    //region Recurrence
    /**
     * Returns a "fake" assignment used to identify a certain occurrence of a recurring event.
     * If passed the original event, it returns `originalAssignment`.
     * @param {Scheduler.model.AssignmentModel} originalAssignment
     * @param {Scheduler.model.EventModel} occurrence
     * @returns {Object} Temporary assignment
     * @internal
     */
    getOccurrence(originalAssignment, occurrence) {
      if (!originalAssignment || !(occurrence == null ? void 0 : occurrence.isOccurrence)) {
        return originalAssignment;
      }
      const me = this;
      return {
        id: `${occurrence.id}:a${originalAssignment.id}`,
        event: occurrence,
        resource: originalAssignment.resource,
        eventId: occurrence.id,
        resourceId: originalAssignment.resource.id,
        isAssignment: true,
        // This field is required to distinguish this fake assignment when event is being removed from UI
        isOccurrenceAssignment: true,
        // Not being an actual record, instanceMeta is stored on the store instead
        instanceMeta(instanceOrId) {
          return me.occurrenceInstanceMeta(this, instanceOrId);
        }
      };
    }
    // Per fake assignment instance meta, stored on store since fakes are always generated on demand
    occurrenceInstanceMeta(occurrenceAssignment, instanceOrId) {
      const me = this, instanceId = instanceOrId.id || instanceOrId, { id: id2 } = occurrenceAssignment;
      let { occurrenceMeta } = me;
      if (!occurrenceMeta) {
        occurrenceMeta = me.occurrenceMeta = {};
      }
      if (!occurrenceMeta[id2]) {
        occurrenceMeta[id2] = {};
      }
      return occurrenceMeta[id2][instanceId] || (occurrenceMeta[id2][instanceId] = {});
    }
    //endregion
    //region Mapping
    /**
     * Maps over event assignments.
     *
     * @param {Scheduler.model.EventModel} event
     * @param {Function} [fn]
     * @param {Function} [filterFn]
     * @returns {Scheduler.model.EventModel[]|Array}
     * @category Assignments
     */
    mapAssignmentsForEvent(event, fn2, filterFn) {
      event = this.eventStore.getById(event);
      const fnSet = Boolean(fn2), filterFnSet = Boolean(filterFn);
      if (fnSet || filterFnSet) {
        return event.assignments.reduce((result, assignment) => {
          const mapResult = fnSet ? fn2(assignment) : assignment;
          if (!filterFnSet || filterFn(mapResult)) {
            result.push(mapResult);
          }
          return result;
        }, []);
      }
      return event.assignments;
    }
    /**
     * Maps over resource assignments.
     *
     * @param {Scheduler.model.ResourceModel|Number|String} resource
     * @param {Function} [fn]
     * @param {Function} [filterFn]
     * @returns {Scheduler.model.ResourceModel[]|Array}
     * @category Assignments
     */
    mapAssignmentsForResource(resource, fn2, filterFn) {
      resource = this.resourceStore.getById(resource);
      const fnSet = Boolean(fn2), filterFnSet = Boolean(filterFn);
      if (fnSet || filterFnSet) {
        return resource.assignments.reduce((result, assignment) => {
          const mapResult = fnSet ? fn2(assignment) : assignment;
          if (!filterFnSet || filterFn(mapResult)) {
            result.push(mapResult);
          }
          return result;
        }, []);
      }
      return resource.assignments;
    }
    // Proper types are documented for each product in their AssignmentStore.js file
    getAssignmentsForEvent(event) {
      return event.assignments;
    }
    /**
     * Removes all assignments for given event
     *
     * @param {Scheduler.model.TimeSpan} event
     * @category Assignments
     */
    removeAssignmentsForEvent(event) {
      return this.remove(event.assignments);
    }
    /**
     * Returns all assignments for a given resource.
     *
     * @param {Scheduler.model.ResourceModel} resource
     * @returns {Scheduler.model.AssignmentModel[]}
     * @category Assignments
     */
    getAssignmentsForResource(resource) {
      resource = this.resourceStore.getById(resource);
      return resource.assignments;
    }
    /**
     * Removes all assignments for given resource
     *
     * @param {Scheduler.model.ResourceModel|*} resource
     * @category Assignments
     */
    removeAssignmentsForResource(resource) {
      this.remove(this.getAssignmentsForResource(resource));
    }
    /**
     * Returns all resources assigned to an event.
     *
     * @param {Scheduler.model.EventModel} event
     * @returns {Scheduler.model.ResourceModel[]}
     * @category Assignments
     */
    getResourcesForEvent(event) {
      return event.resources;
    }
    /**
     * Returns all events assigned to a resource
     *
     * @param {Scheduler.model.ResourceModel|String|Number} resource
     * @returns {Scheduler.model.TimeSpan[]}
     * @category Assignments
     */
    getEventsForResource(resource) {
      resource = this.resourceStore.getById(resource);
      return resource == null ? void 0 : resource.events;
    }
    /**
     * Creates and adds assignment record(s) for a given event and resource(s).
     *
     * @param {Scheduler.model.TimeSpan} event
     * @param {Scheduler.model.ResourceModel|Scheduler.model.ResourceModel[]} resources The resource(s) to assign to the event
     * @param {Function} [assignmentSetupFn] A hook function which takes an assignment as its argument and must return an assignment.
     * @param {Boolean} [removeExistingAssignments] `true` to remove assignments for other resources
     * @returns {Scheduler.model.AssignmentModel[]} An array with the created assignment(s)
     * @category Assign
     */
    assignEventToResource(event, resources, assignmentSetupFn = null, removeExistingAssignments = false) {
      var _a6, _b2, _c2;
      const me = this, toRemove = removeExistingAssignments ? new Set(event.assignments) : null;
      resources = ArrayHelper.asArray(resources).map((r) => {
        var _a7;
        return (_a7 = r.$original) != null ? _a7 : r;
      });
      if ((_a6 = me.eventStore) == null ? void 0 : _a6.usesSingleAssignment) {
        if ((_b2 = event.assignments) == null ? void 0 : _b2.length) {
          if (!me.isEventAssignedToResource(event, resources[0])) {
            event.resource = resources[0];
          }
          return [];
        } else {
          event.resourceId = (_c2 = resources[0]) == null ? void 0 : _c2.id;
        }
      }
      let newAssignments = [];
      me.suspendAutoCommit();
      resources.forEach((resource) => {
        var _a7;
        const existingAssignment = me.getAssignmentForEventAndResource(event, resource);
        if (!existingAssignment) {
          const assignment = {
            event,
            resource
          };
          newAssignments.push((_a7 = assignmentSetupFn == null ? void 0 : assignmentSetupFn(assignment)) != null ? _a7 : assignment);
        } else if (removeExistingAssignments) {
          toRemove.delete(existingAssignment);
        }
      });
      newAssignments = me.add(newAssignments);
      if (removeExistingAssignments) {
        me.remove(Array.from(toRemove));
      }
      me.resumeAutoCommit();
      return newAssignments;
    }
    /**
     * Removes assignment record for a given event and resource.
     *
     * @param {Scheduler.model.TimeSpan|String|Number} event
     * @param {Scheduler.model.ResourceModel|String|Number} [resources] The resource to unassign the event from. If omitted, all resources of the events will be unassigned
     * @returns {Scheduler.model.AssignmentModel|Scheduler.model.AssignmentModel[]}
     * @category Assign
     */
    unassignEventFromResource(event, resources) {
      const me = this, assignmentsToRemove = [];
      if (!resources) {
        return me.removeAssignmentsForEvent(event);
      }
      resources = ArrayHelper.asArray(resources);
      for (let i = 0; i < resources.length; i++) {
        if (me.isEventAssignedToResource(event, resources[i])) {
          assignmentsToRemove.push(me.getAssignmentForEventAndResource(event, resources[i]));
        }
      }
      return me.remove(assignmentsToRemove);
    }
    /**
     * Checks whether an event is assigned to a resource.
     *
     * @param {Scheduler.model.EventModel|String|Number} event Event record or id
     * @param {Scheduler.model.ResourceModel|String|Number} resource Resource record or id
     * @returns {Boolean}
     * @category Assignments
     */
    isEventAssignedToResource(event, resource) {
      return Boolean(this.getAssignmentForEventAndResource(event, resource));
    }
    /**
     * Returns an assignment record for a given event and resource
     *
     * @param {Scheduler.model.EventModel|String|Number} event The event or its id
     * @param {Scheduler.model.ResourceModel|String|Number} resource The resource or its id
     * @returns {Scheduler.model.AssignmentModel}
     * @category Assignments
     */
    getAssignmentForEventAndResource(event, resource) {
      let assignments;
      if (!(event = this.eventStore.getById(event)) || !(assignments = event.assignments) || // Also note that resources are looked for in the master store if chained, to handle dragging between
      // schedulers using chained versions of the same resource store. Needed since assignmentStore is shared and
      // might point to wrong resourceStore (can only point to one)
      !(resource = this.resourceStore.$master.getById(resource))) {
        return null;
      }
      return this.getOccurrence(assignments.find((a) => {
        var _a6;
        return ((_a6 = a.resource) == null ? void 0 : _a6.$original) === resource.$original;
      }), event);
    }
    //endregion
  }, __publicField(_a5, "$name", "AssignmentStoreMixin"), _a5;
};

// ../Engine/lib/Engine/util/Functions.js
var isNotNumber = (value2) => Number(value2) !== value2;
var CIFromSetOrArrayOrValue = (value2) => {
  if (value2 instanceof Set || value2 instanceof Array)
    return CI(value2);
  return CI([value2]);
};
var delay = (value2) => new Promise((resolve) => setTimeout(resolve, value2));
var format = (format2, ...values) => {
  return format2.replace(/{(\d+)}/g, (match, number) => typeof values[number] !== "undefined" ? values[number] : match);
};

// ../Engine/lib/Engine/quark/store/AbstractAssignmentStoreMixin.js
var AbstractAssignmentStoreMixin = class extends Mixin([AbstractPartOfProjectStoreMixin], (base) => {
  const superProto2 = base.prototype;
  class AbstractAssignmentStoreMixin2 extends base {
    constructor() {
      super(...arguments);
      this.assignmentsForRemoval = /* @__PURE__ */ new Set();
      this.allAssignmentsForRemoval = false;
    }
    remove(records, silent) {
      this.assignmentsForRemoval = CIFromSetOrArrayOrValue(records).toSet();
      const res = superProto2.remove.call(this, records, silent);
      this.assignmentsForRemoval.clear();
      return res;
    }
    removeAll(silent) {
      this.allAssignmentsForRemoval = true;
      const res = superProto2.removeAll.call(this, silent);
      this.allAssignmentsForRemoval = false;
      return res;
    }
  }
  return AbstractAssignmentStoreMixin2;
}) {
};

// ../Engine/lib/Engine/quark/store/CoreAssignmentStoreMixin.js
var emptySet = /* @__PURE__ */ new Set();
var CoreAssignmentStoreMixin = class extends Mixin([AbstractAssignmentStoreMixin, CorePartOfProjectStoreMixin], (base) => {
  const superProto2 = base.prototype;
  class CoreAssignmentStoreMixin2 extends base {
    constructor() {
      super(...arguments);
      this.skipInvalidateIndices = false;
    }
    static get defaultConfig() {
      return {
        modelClass: CoreAssignmentMixin,
        storage: {
          extraKeys: [
            { property: "event", unique: false },
            { property: "resource", unique: false },
            { property: "eventId", unique: false }
          ]
        }
      };
    }
    set data(value2) {
      this.allAssignmentsForRemoval = true;
      super.data = value2;
      this.allAssignmentsForRemoval = false;
    }
    getEventsAssignments(event) {
      return this.storage.findItem("event", event, true) || emptySet;
    }
    getResourcesAssignments(resource) {
      return this.storage.findItem("resource", resource.$original, true) || emptySet;
    }
    updateIndices() {
      this.storage.rebuildIndices();
    }
    invalidateIndices() {
      this.storage.invalidateIndices();
    }
    afterLoadData() {
      this.eventStore && this.linkAssignments(this.eventStore, "event");
      this.resourceStore && this.linkAssignments(this.resourceStore, "resource");
    }
    // Link events/resources to assignments, called when those stores are populated or joined to project
    linkAssignments(store, modelName) {
      store = store.masterStore || store;
      const unresolved = this.count && this.storage.findItem(modelName, null, true);
      if (unresolved) {
        for (const assignment of unresolved) {
          const record = store.getById(assignment.getCurrentOrProposed(modelName));
          if (record)
            assignment.setChanged(modelName, record);
        }
        this.invalidateIndices();
      }
    }
    // Unlink events/resources from assignments, called when those stores are cleared
    unlinkAssignments(modelName) {
      this.forEach((assignment) => {
        var _a5, _b2, _c2;
        return assignment.setChanged(modelName, (_c2 = (_b2 = (_a5 = assignment[modelName]) == null ? void 0 : _a5.id) != null ? _b2 : assignment == null ? void 0 : assignment.getData(modelName)) != null ? _c2 : assignment[modelName + "Id"]);
      });
      this.invalidateIndices();
    }
    onCommitAsync() {
      this.updateIndices();
    }
  }
  return CoreAssignmentStoreMixin2;
}) {
};

// ../Scheduler/lib/Scheduler/data/AssignmentStore.js
var EngineMixin6 = PartOfProject_default(CoreAssignmentStoreMixin.derive(AjaxStore));
var AssignmentStore = class extends AssignmentStoreMixin_default(EngineMixin6) {
  static get defaultConfig() {
    return {
      modelClass: AssignmentModel
    };
  }
};
/**
 * Returns all assignments for a given event.
 * @function getAssignmentsForEvent
 * @param {Scheduler.model.EventModel} event The event to get assignments for
 * @returns {Scheduler.model.AssignmentModel[]} Array of assignments for the event
 * @category Assignments
 */
__publicField(AssignmentStore, "$name", "AssignmentStore");
AssignmentStore._$name = "AssignmentStore";

// ../Scheduler/lib/Scheduler/model/DependencyBaseModel.js
var canonicalDependencyTypes = [
  "SS",
  "SF",
  "FS",
  "FF"
];
var _DependencyBaseModel = class _DependencyBaseModel extends Model {
  /**
   * Set value for the specified field(s), triggering engine calculations immediately. See
   * {@link Core.data.Model#function-set Model#set()} for arguments.
   **
   * ```javascript
   * dependency.set('from', 2);
   * // dependency.fromEvent is not yet up to date
   *
   * await dependency.setAsync('from', 2);
   * // dependency.fromEvent is up to date
   * ```
   *
   * @param {String|Object} field The field to set value for, or an object with multiple values to set in one call
   * @param {*} [value] Value to set
   * @param {Boolean} [silent=false] Set to `true` to not trigger events
   * automatically.
   * @function setAsync
   * @category Editing
   * @async
   */
  //region Fields
  /**
   * An enumerable object, containing names for the dependency types integer constants.
   * - 0 StartToStart
   * - 1 StartToEnd
   * - 2 EndToStart
   * - 3 EndToEnd
   * @property {DependencyType}
   * @readonly
   * @category Dependency
   */
  static get Type() {
    return {
      StartToStart: 0,
      StartToEnd: 1,
      EndToStart: 2,
      EndToEnd: 3
    };
  }
  static get fields() {
    return [
      // 3 mandatory fields
      /**
       * From event, id of source event
       * @field {String|Number} from
       * @category Dependency
       */
      { name: "from" },
      /**
       * To event, id of target event
       * @field {String|Number} to
       * @category Dependency
       */
      { name: "to" },
      /**
       * Dependency type, see static property {@link #property-Type-static}
       * @field {Number} type=2
       * @category Dependency
       */
      { name: "type", type: "int", defaultValue: 2 },
      /**
       * CSS class to apply to lines drawn for the dependency
       * @field {String} cls
       * @category Styling
       */
      { name: "cls", defaultValue: "" },
      /**
       * Bidirectional, drawn with arrows in both directions
       * @field {Boolean} bidirectional
       * @category Dependency
       */
      { name: "bidirectional", type: "boolean" },
      /**
       * Start side on source (top, left, bottom, right)
       * @field {'top'|'left'|'bottom'|'right'} fromSide
       * @category Dependency
       */
      { name: "fromSide", type: "string" },
      /**
       * End side on target (top, left, bottom, right)
       * @field {'top'|'left'|'bottom'|'right'} toSide
       * @category Dependency
       */
      { name: "toSide", type: "string" },
      /**
       * The magnitude of this dependency's lag (the number of units).
       * @field {Number} lag
       * @category Dependency
       */
      { name: "lag", type: "number", allowNull: true, defaultValue: 0 },
      /**
       * The units of this dependency's lag, defaults to "d" (days). Valid values are:
       *
       * - "ms" (milliseconds)
       * - "s" (seconds)
       * - "m" (minutes)
       * - "h" (hours)
       * - "d" (days)
       * - "w" (weeks)
       * - "M" (months)
       * - "y" (years)
       *
       * This field is readonly after creation, to change `lagUnit` use {@link #function-setLag setLag()}.
       * @field {DurationUnit|DurationUnitShort} lagUnit
       * @category Dependency
       * @readonly
       */
      {
        name: "lagUnit",
        type: "string",
        defaultValue: "d"
      },
      { name: "highlighted", persist: false, internal: true }
    ];
  }
  // fromEvent/toEvent defined in CoreDependencyMixin in engine
  /**
   * Gets/sets the source event of the dependency.
   *
   * Accepts multiple formats but always returns an {@link Scheduler.model.EventModel}.
   *
   * **NOTE:** This is not a proper field but rather an alias, it will be serialized but cannot be remapped. If you
   * need to remap, consider using {@link #field-from} instead.
   *
   * @field {Scheduler.model.EventModel} fromEvent
   * @accepts {String|Number|Scheduler.model.EventModel}
   * @category Dependency
   */
  /**
   * Gets/sets the target event of the dependency.
   *
   * Accepts multiple formats but always returns an {@link Scheduler.model.EventModel}.
   *
   * **NOTE:** This is not a proper field but rather an alias, it will be serialized but cannot be remapped. If you
   * need to remap, consider using {@link #field-to} instead.
   *
   * @field {Scheduler.model.EventModel} toEvent
   * @accepts {String|Number|Scheduler.model.EventModel}
   * @category Dependency
   */
  //endregion
  //region Init
  construct(data) {
    const from = data[this.fieldMap.from.dataSource], to = data[this.fieldMap.to.dataSource], typeFieldType = this.fieldMap.type.type;
    if (typeFieldType && typeFieldType !== "int" && typeFieldType !== "integer") {
      throw new Error('Dependency type field must be of type "int"');
    }
    if (from != null) {
      data.fromEvent = from;
    }
    if (to != null) {
      data.toEvent = to;
    }
    super.construct(...arguments);
  }
  //endregion
  get eventStore() {
    var _a5;
    return this.eventStore || ((_a5 = this.unjoinedStores[0]) == null ? void 0 : _a5.eventStore);
  }
  set from(value2) {
    const { fromEvent } = this;
    if ((fromEvent == null ? void 0 : fromEvent.isModel) && fromEvent.id === value2) {
      this.set("from", value2);
    } else {
      this.fromEvent = value2;
    }
  }
  get from() {
    return this.get("from");
  }
  set to(value2) {
    const { toEvent } = this;
    if ((toEvent == null ? void 0 : toEvent.isModel) && toEvent.id === value2) {
      this.set("to", value2);
    } else {
      this.toEvent = value2;
    }
  }
  get to() {
    return this.get("to");
  }
  /**
   * Alias to dependency type, but when set resets {@link #field-fromSide} & {@link #field-toSide} to null as well.
   *
   * @property {Number}
   * @category Dependency
   */
  get hardType() {
    return this.getHardType();
  }
  set hardType(type) {
    this.setHardType(type);
  }
  /**
   * Returns dependency hard type, see {@link #property-hardType}.
   *
   * @returns {Number}
   * @category Dependency
   */
  getHardType() {
    return this.get("type");
  }
  /**
   * Sets dependency {@link #field-type} and resets {@link #field-fromSide} and {@link #field-toSide} to null.
   *
   * @param {Number} type
   * @category Dependency
   */
  setHardType(type) {
    let result;
    if (type !== this.hardType) {
      result = this.set({
        type,
        fromSide: null,
        toSide: null
      });
    }
    return result;
  }
  get lag() {
    return this.get("lag");
  }
  set lag(lag) {
    this.setLag(lag);
  }
  /**
   * Sets lag and lagUnit in one go. Only allowed way to change lagUnit, the lagUnit field is readonly after creation
   * @param {Number|String|Object} lag The lag value. May be just a numeric magnitude, or a full string descriptor eg '1d'
   * @param {DurationUnitShort} [lagUnit] Unit for numeric lag value, see
   * {@link #field-lagUnit} for valid values
   * @category Dependency
   */
  setLag(lag, lagUnit = this.lagUnit) {
    if (arguments.length === 1) {
      if (typeof lag === "number") {
        this.lag = lag;
      } else {
        lag = DateHelper.parseDuration(lag);
        this.set({
          lag: lag.magnitude,
          lagUnit: lag.unit
        });
      }
      return;
    }
    lag = parseFloat(lag);
    this.set({
      lag,
      lagUnit
    });
  }
  getLag() {
    if (this.lag) {
      return `${this.lag < 0 ? "-" : "+"}${Math.abs(this.lag)}${DateHelper.getShortNameOfUnit(this.lagUnit)}`;
    }
    return "";
  }
  /**
   * Property which encapsulates the lag's magnitude and units. An object which contains two properties:
   * @property {Core.data.Duration}
   * @property {Number} fullLag.magnitude The magnitude of the duration
   * @property {DurationUnitShort} fullLag.unit The unit in which the duration is measured, eg
   * `'d'` for days
   * @category Dependency
   */
  get fullLag() {
    return new Duration({
      unit: this.lagUnit,
      magnitude: this.lag
    });
  }
  set fullLag(lag) {
    if (typeof lag === "string") {
      this.setLag(lag);
    } else {
      this.setLag(lag.magnitude, lag.unit);
    }
  }
  /**
   * Returns true if the linked events have been persisted (e.g. neither of them are 'phantoms')
   *
   * @property {Boolean}
   * @readonly
   * @category Editing
   */
  get isPersistable() {
    const me = this, { stores: stores2, unjoinedStores } = me, store = stores2[0];
    let result;
    if (store) {
      const { fromEvent, toEvent } = me, crudManager = store.crudManager;
      result = fromEvent && (crudManager || !fromEvent.hasGeneratedId) && toEvent && (crudManager || !toEvent.hasGeneratedId);
    } else {
      result = Boolean(unjoinedStores[0]);
    }
    return result && super.isPersistable;
  }
  getDateRange() {
    const { fromEvent, toEvent } = this;
    if ((fromEvent == null ? void 0 : fromEvent.isScheduled) && (toEvent == null ? void 0 : toEvent.isScheduled)) {
      const Type = _DependencyBaseModel.Type;
      let sourceDate, targetDate;
      switch (this.type) {
        case Type.StartToStart:
          sourceDate = fromEvent.startDateMS;
          targetDate = toEvent.startDateMS;
          break;
        case Type.StartToEnd:
          sourceDate = fromEvent.startDateMS;
          targetDate = toEvent.endDateMS;
          break;
        case Type.EndToEnd:
          sourceDate = fromEvent.endDateMS;
          targetDate = toEvent.endDateMS;
          break;
        case Type.EndToStart:
          sourceDate = fromEvent.endDateMS;
          targetDate = toEvent.startDateMS;
          break;
        default:
          throw new Error("Invalid dependency type: " + this.type);
      }
      return {
        start: Math.min(sourceDate, targetDate),
        end: Math.max(sourceDate, targetDate)
      };
    }
    return null;
  }
  /**
   * Applies given CSS class to dependency, the value doesn't persist
   *
   * @param {String} cls
   * @category Dependency
   */
  highlight(cls) {
    var _a5, _b2;
    const classes = (_b2 = (_a5 = this.highlighted) == null ? void 0 : _a5.split(" ")) != null ? _b2 : [];
    if (!classes.includes(cls)) {
      this.highlighted = classes.concat(cls).join(" ");
    }
  }
  /**
   * Removes given CSS class from dependency if applied, the value doesn't persist
   *
   * @param {String} cls
   * @category Dependency
   */
  unhighlight(cls) {
    const { highlighted } = this;
    if (highlighted) {
      const classes = highlighted.split(" "), index = classes.indexOf(cls);
      if (index >= 0) {
        classes.splice(index, 1);
        this.highlighted = classes.join(" ");
      }
    }
  }
  /**
   * Checks if the given CSS class is applied to dependency.
   *
   * @param {String} cls
   * @returns {Boolean}
   * @category Dependency
   */
  isHighlightedWith(cls) {
    return this.highlighted && this.highlighted.split(" ").includes(cls);
  }
  getConnectorString(raw) {
    const rawValue = canonicalDependencyTypes[this.type];
    if (raw) {
      return rawValue;
    }
    if (this.type === _DependencyBaseModel.Type.EndToStart) {
      return "";
    }
    return rawValue;
  }
  // getConnectorStringFromType(type, raw) {
  //     const rawValue = canonicalDependencyTypes[type];
  //
  //     if (raw) {
  //         return rawValue;
  //     }
  //
  //     // FS => empty string; it's the default
  //     if (type === DependencyBaseModel.Type.EndToStart) {
  //         return '';
  //     }
  //
  //     const locale = LocaleManager.locale;
  //
  //     // See if there is a local version of SS, SF or FF
  //     if (locale) {
  //         const localized = locale.Scheduler && locale.Scheduler[rawValue];
  //         if (localized) {
  //             return localized;
  //         }
  //     }
  //
  //     return rawValue;
  // }
  // getConnectorString(raw) {
  //     return this.getConnectorStringFromType(this.type);
  // }
  // * getConnectorStringGenerator(raw) {
  //     return this.getConnectorStringFromType(yield this.$.type);
  // }
  toString() {
    return `${this.from}${this.getConnectorString()}${this.getLag()}`;
  }
  /**
   * Returns `true` if the dependency is valid. It is considered valid if it has a valid type and both from and to
   * events are set and pointing to different events.
   *
   * @property {Boolean}
   * @typings ignore
   * @category Editing
   */
  get isValid() {
    const { fromEvent, toEvent, type } = this;
    return typeof type === "number" && fromEvent && toEvent && fromEvent !== toEvent;
  }
  get fromEventName() {
    var _a5;
    return ((_a5 = this.fromEvent) == null ? void 0 : _a5.name) || "";
  }
  get toEventName() {
    var _a5;
    return ((_a5 = this.toEvent) == null ? void 0 : _a5.name) || "";
  }
  //region STM hooks
  shouldRecordFieldChange(fieldName, oldValue, newValue) {
    var _a5;
    if (!super.shouldRecordFieldChange(fieldName, oldValue, newValue)) {
      return false;
    }
    if (fieldName === "from" || fieldName === "to" || fieldName === "fromEvent" || fieldName === "toEvent") {
      const eventStore = (_a5 = this.project) == null ? void 0 : _a5.eventStore;
      if (eventStore && eventStore.oldIdMap[oldValue] === eventStore.getById(newValue)) {
        return false;
      }
    }
    return true;
  }
  //endregion
  get skipCopyFields() {
    var _a5, _b2, _c2, _d;
    const { fieldMap } = this;
    return {
      [fieldMap.from.dataSource]: true,
      [fieldMap.to.dataSource]: true,
      [(_a5 = fieldMap.fromEvent) == null ? void 0 : _a5.dataSource]: true,
      [(_b2 = fieldMap.toEvent) == null ? void 0 : _b2.dataSource]: true,
      [(_c2 = fieldMap.fromTask) == null ? void 0 : _c2.dataSource]: true,
      [(_d = fieldMap.toTask) == null ? void 0 : _d.dataSource]: true
    };
  }
};
__publicField(_DependencyBaseModel, "$name", "DependencyBaseModel");
var DependencyBaseModel = _DependencyBaseModel;
DependencyBaseModel.exposeProperties();
DependencyBaseModel._$name = "DependencyBaseModel";

// ../Engine/lib/Engine/quark/model/scheduler_core/CoreDependencyMixin.js
var CoreDependencyMixin = class extends Mixin([CorePartOfProjectModelMixin], (base) => {
  const superProto2 = base.prototype;
  class CoreDependencyMixin2 extends base {
    static get fields() {
      return [
        { name: "fromEvent", isEqual: (a, b) => a === b, persist: false },
        { name: "toEvent", isEqual: (a, b) => a === b, persist: false }
      ];
    }
    // Resolve early + update indices to have buckets ready before commit
    setChanged(field, value2, invalidate) {
      var _a5, _b2, _c2;
      let update = false;
      if (field === "fromEvent" || field === "toEvent") {
        const event = isInstanceOf(value2, CoreEventMixin) ? value2 : (_a5 = this.eventStore) == null ? void 0 : _a5.getById(value2);
        if (event)
          update = true;
        value2 = event || value2;
      }
      superProto2.setChanged.call(this, field, value2, invalidate, true);
      if (update && !this.project.isPerformingCommit && !((_b2 = this.dependencyStore) == null ? void 0 : _b2.isLoadingData)) {
        (_c2 = this.dependencyStore) == null ? void 0 : _c2.invalidateIndices();
      }
    }
    // Resolve events when joining project
    joinProject() {
      superProto2.joinProject.call(this);
      this.setChanged("fromEvent", this.get("fromEvent"));
      this.setChanged("toEvent", this.get("toEvent"));
    }
    // Resolved events as part of commit
    // Normally done earlier in setChanged, but stores might not have been available yet at that point
    calculateInvalidated() {
      var _a5, _b2;
      let { fromEvent, toEvent } = this.$changed;
      if (fromEvent !== null && !isInstanceOf(fromEvent, CoreEventMixin)) {
        const resolved = (_a5 = this.eventStore) == null ? void 0 : _a5.getById(fromEvent);
        if (resolved)
          this.$changed.fromEvent = resolved;
      }
      if (toEvent !== null && !isInstanceOf(toEvent, CoreEventMixin)) {
        const resolved = (_b2 = this.eventStore) == null ? void 0 : _b2.getById(toEvent);
        if (resolved)
          this.$changed.toEvent = resolved;
      }
    }
    //region Events
    // Not using "propose" mechanism from CoreEventMixin, because buckets are expected to be up to date right away
    set fromEvent(fromEvent) {
      this.setChanged("fromEvent", fromEvent);
    }
    get fromEvent() {
      const fromEvent = this.get("fromEvent");
      return (fromEvent == null ? void 0 : fromEvent.id) != null ? fromEvent : null;
    }
    set toEvent(toEvent) {
      this.setChanged("toEvent", toEvent);
    }
    get toEvent() {
      const toEvent = this.get("toEvent");
      return (toEvent == null ? void 0 : toEvent.id) != null ? toEvent : null;
    }
  }
  return CoreDependencyMixin2;
}) {
};

// ../Scheduler/lib/Scheduler/model/DependencyModel.js
var EngineMixin7 = CoreDependencyMixin;
var DependencyModel = class extends PartOfProject_default(EngineMixin7.derive(DependencyBaseModel)) {
  // Determines the type of dependency based on fromSide and toSide
  getTypeFromSides(fromSide, toSide, rtl) {
    const types = DependencyBaseModel.Type, startSide = rtl ? "right" : "left", endSide = rtl ? "left" : "right";
    if (fromSide === startSide) {
      return toSide === startSide ? types.StartToStart : types.StartToEnd;
    }
    return toSide === endSide ? types.EndToEnd : types.EndToStart;
  }
};
__publicField(DependencyModel, "$name", "DependencyModel");
DependencyModel.exposeProperties();
DependencyModel._$name = "DependencyModel";

// ../Scheduler/lib/Scheduler/data/mixin/DependencyStoreMixin.js
var DependencyStoreMixin_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    /**
     * Add dependencies to the store.
     *
     * NOTE: References (fromEvent, toEvent) on the dependencies are determined async by a calculation engine. Thus they
     * cannot be directly accessed after using this function.
     *
     * For example:
     *
     * ```javascript
     * const [dependency] = dependencyStore.add({ from, to });
     * // dependency.fromEvent is not yet available
     * ```
     *
     * To guarantee references are set up, wait for calculations for finish:
     *
     * ```javascript
     * const [dependency] = dependencyStore.add({ from, to });
     * await dependencyStore.project.commitAsync();
     * // dependency.fromEvent is available (assuming EventStore is loaded and so on)
     * ```
     *
     * Alternatively use `addAsync()` instead:
     *
     * ```javascript
     * const [dependency] = await dependencyStore.addAsync({ from, to });
     * // dependency.fromEvent is available (assuming EventStore is loaded and so on)
     * ```
     *
     * @param {Scheduler.model.DependencyModel|Scheduler.model.DependencyModel[]|DependencyModelConfig|DependencyModelConfig[]} records
     * Array of records/data or a single record/data to add to store
     * @param {Boolean} [silent] Specify `true` to suppress events
     * @returns {Scheduler.model.DependencyModel[]} Added records
     * @function add
     * @category CRUD
     */
    /**
     * Add dependencies to the store and triggers calculations directly after. Await this function to have up to date
     * references on the added dependencies.
     *
     * ```javascript
     * const [dependency] = await dependencyStore.addAsync({ from, to });
     * // dependency.fromEvent is available (assuming EventStore is loaded and so on)
     * ```
     *
     * @param {Scheduler.model.DependencyModel|Scheduler.model.DependencyModel[]|DependencyModelConfig|DependencyModelConfig[]} records
     * Array of records/data or a single record/data to add to store
     * @param {Boolean} [silent] Specify `true` to suppress events
     * @returns {Scheduler.model.DependencyModel[]} Added records
     * @function addAsync
     * @category CRUD
     * @async
     */
    /**
     * Applies a new dataset to the DependencyStore. Use it to plug externally fetched data into the store.
     *
     * NOTE: References (fromEvent, toEvent) on the dependencies are determined async by a calculation engine. Thus
     * they cannot be directly accessed after assigning the new dataset.
     *
     * For example:
     *
     * ```javascript
     * dependencyStore.data = [{ from, to }];
     * // dependencyStore.first.fromEvent is not yet available
     * ```
     *
     * To guarantee references are available, wait for calculations for finish:
     *
     * ```javascript
     * dependencyStore.data = [{ from, to }];
     * await dependencyStore.project.commitAsync();
     * // dependencyStore.first.fromEvent is available
     * ```
     *
     * Alternatively use `loadDataAsync()` instead:
     *
     * ```javascript
     * await dependencyStore.loadDataAsync([{ from, to }]);
     * // dependencyStore.first.fromEvent is available
     * ```
     *
     * @member {DependencyModelConfig[]} data
     * @category Records
     */
    /**
     * Applies a new dataset to the DependencyStore and triggers calculations directly after. Use it to plug externally
     * fetched data into the store.
     *
     * ```javascript
     * await dependencyStore.loadDataAsync([{ from, to }]);
     * // dependencyStore.first.fromEvent is available
     * ```
     *
     * @param {DependencyModelConfig[]} data Array of DependencyModel data objects
     * @function loadDataAsync
     * @category CRUD
     * @async
     */
    static get defaultConfig() {
      return {
        /**
         * CrudManager must load stores in the correct order. Lowest first.
         * @private
         */
        loadPriority: 400,
        /**
         * CrudManager must sync stores in the correct order. Lowest first.
         * @private
         */
        syncPriority: 400,
        id: "dependencies"
      };
    }
    reduceEventDependencies(event, reduceFn, result, flat = true, depsGetterFn) {
      depsGetterFn = depsGetterFn || ((event2) => this.getEventDependencies(event2));
      event = ArrayHelper.asArray(event);
      event.reduce((result2, event2) => {
        if (event2.children && !flat) {
          event2.traverse((evt) => {
            result2 = depsGetterFn(evt).reduce(reduceFn, result2);
          });
        } else {
          result2 = depsGetterFn(event2).reduce(reduceFn, result2);
        }
      }, result);
      return result;
    }
    mapEventDependencies(event, fn2, filterFn, flat, depsGetterFn) {
      return this.reduceEventDependencies(event, (result, dependency) => {
        filterFn(dependency) && result.push(dependency);
        return result;
      }, [], flat, depsGetterFn);
    }
    mapEventPredecessors(event, fn2, filterFn, flat) {
      return this.reduceEventPredecessors(event, (result, dependency) => {
        filterFn(dependency) && result.push(dependency);
        return result;
      }, [], flat);
    }
    mapEventSuccessors(event, fn2, filterFn, flat) {
      return this.reduceEventSuccessors(event, (result, dependency) => {
        filterFn(dependency) && result.push(dependency);
        return result;
      }, [], flat);
    }
    /**
     * Returns all dependencies for a certain event (both incoming and outgoing)
     *
     * @param {Scheduler.model.EventModel} event
     * @returns {Scheduler.model.DependencyModel[]}
     */
    getEventDependencies(event) {
      return [].concat(event.predecessors || [], event.successors || []);
    }
    removeEventDependencies(event) {
      this.remove(this.getEventDependencies(event));
    }
    removeEventPredecessors(event) {
      this.remove(event.predecessors);
    }
    removeEventSuccessors(event, flat) {
      this.remove(event.successors);
    }
    getBySourceTargetId(key) {
      return this.records.find(
        (r) => key == this.constructor.makeDependencySourceTargetCompositeKey(r.from, r.to)
      );
    }
    /**
     * Returns dependency model instance linking tasks with given ids. The dependency can be forward (from 1st
     * task to 2nd) or backward (from 2nd to 1st).
     *
     * @param {Scheduler.model.EventModel|String} sourceEvent 1st event
     * @param {Scheduler.model.EventModel|String} targetEvent 2nd event
     * @returns {Scheduler.model.DependencyModel}
     */
    getDependencyForSourceAndTargetEvents(sourceEvent, targetEvent) {
      sourceEvent = Model.asId(sourceEvent);
      targetEvent = Model.asId(targetEvent);
      return this.getBySourceTargetId(this.constructor.makeDependencySourceTargetCompositeKey(sourceEvent, targetEvent));
    }
    /**
     * Returns a dependency model instance linking given events if such dependency exists in the store.
     * The dependency can be forward (from 1st event to 2nd) or backward (from 2nd to 1st).
     *
     * @param {Scheduler.model.EventModel|String} sourceEvent
     * @param {Scheduler.model.EventModel|String} targetEvent
     * @returns {Scheduler.model.DependencyModel}
     */
    getEventsLinkingDependency(sourceEvent, targetEvent) {
      return this.getDependencyForSourceAndTargetEvents(sourceEvent, targetEvent) || this.getDependencyForSourceAndTargetEvents(targetEvent, sourceEvent);
    }
    /**
     * Validation method used to validate a dependency. Override and return `true` to indicate that an
     * existing dependency between two tasks is valid. For a new dependency being created please see
     * {@link #function-isValidDependencyToCreate}.
     *
     * @param {Scheduler.model.DependencyModel|Scheduler.model.TimeSpan|Number|String} dependencyOrFromId The dependency
     * model, the from task/event or the id of the from task/event
     * @param {Scheduler.model.TimeSpan|Number|String} [toId] To task/event or id thereof if the first parameter is not
     * a dependency record
     * @param {Number} [type] Dependency {@link Scheduler.model.DependencyBaseModel#property-Type-static} if the first
     * parameter is not a dependency model instance.
     * @returns {Boolean}
     */
    async isValidDependency(dependencyOrFromId, toId, type) {
      let fromEvent = dependencyOrFromId, toEvent = toId;
      if (dependencyOrFromId == null) {
        return false;
      }
      if (dependencyOrFromId.isDependencyModel) {
        ({ fromEvent, toEvent } = dependencyOrFromId);
      }
      fromEvent = this.eventStore.getById(fromEvent);
      toEvent = this.eventStore.getById(toEvent);
      if (fromEvent && toEvent) {
        if (!fromEvent.project || !toEvent.project) {
          return false;
        }
        return this.project.isValidDependency(fromEvent, toEvent, type);
      }
      return dependencyOrFromId !== toId;
    }
    /**
     * Validation method used to validate a dependency while creating. Override and return `true` to indicate that
     * a new dependency is valid to be created.
     *
     * @param {Scheduler.model.TimeSpan|Number|String} fromId From event/task or id
     * @param {Scheduler.model.TimeSpan|Number|String} toId To event/task or id
     * @param {Number} type Dependency {@link Scheduler.model.DependencyBaseModel#property-Type-static}
     * @returns {Boolean}
     */
    isValidDependencyToCreate(fromId, toId, type) {
      return this.isValidDependency(fromId, toId, type);
    }
    /**
     * Returns all dependencies highlighted with the given CSS class
     *
     * @param {String} cls
     * @returns {Scheduler.model.DependencyBaseModel[]}
     */
    getHighlightedDependencies(cls) {
      return this.records.reduce((result, dep) => {
        if (dep.isHighlightedWith(cls))
          result.push(dep);
        return result;
      }, []);
    }
    static makeDependencySourceTargetCompositeKey(from, to) {
      return `source(${from})-target(${to})`;
    }
    //region Product neutral
    getTimeSpanDependencies(record) {
      return this.getEventDependencies(record);
    }
    //endregion
  }, __publicField(_a5, "$name", "DependencyStoreMixin"), _a5;
};

// ../Engine/lib/Engine/quark/store/AbstractDependencyStoreMixin.js
var AbstractDependencyStoreMixin = class extends Mixin([AbstractPartOfProjectStoreMixin], (base) => {
  const superProto2 = base.prototype;
  class AbstractDependencyStoreMixin2 extends base {
    constructor() {
      super(...arguments);
      this.dependenciesForRemoval = /* @__PURE__ */ new Set();
      this.allDependenciesForRemoval = false;
    }
    remove(records, silent) {
      this.dependenciesForRemoval = CIFromSetOrArrayOrValue(records).toSet();
      const res = superProto2.remove.call(this, records, silent);
      this.dependenciesForRemoval.clear();
      return res;
    }
    removeAll(silent) {
      this.allDependenciesForRemoval = true;
      const res = superProto2.removeAll.call(this, silent);
      this.allDependenciesForRemoval = false;
      return res;
    }
  }
  return AbstractDependencyStoreMixin2;
}) {
};

// ../Engine/lib/Engine/quark/store/CoreDependencyStoreMixin.js
var emptySet2 = /* @__PURE__ */ new Set();
var CoreDependencyStoreMixin = class extends Mixin([AbstractDependencyStoreMixin, CorePartOfProjectStoreMixin], (base) => {
  const superProto2 = base.prototype;
  class CoreDependencyStoreMixin2 extends base {
    constructor() {
      super(...arguments);
      this.dependenciesForRemoval = /* @__PURE__ */ new Set();
      this.allDependenciesForRemoval = false;
    }
    static get defaultConfig() {
      return {
        modelClass: CoreDependencyMixin,
        storage: {
          extraKeys: [
            { property: "fromEvent", unique: false },
            { property: "toEvent", unique: false }
          ]
        }
      };
    }
    getIncomingDepsForEvent(event) {
      return this.storage.findItem("toEvent", event) || emptySet2;
    }
    getOutgoingDepsForEvent(event) {
      return this.storage.findItem("fromEvent", event) || emptySet2;
    }
    set data(value2) {
      this.allDependenciesForRemoval = true;
      super.data = value2;
      this.allDependenciesForRemoval = false;
    }
    updateIndices() {
      this.storage.rebuildIndices();
    }
    invalidateIndices() {
      this.storage.invalidateIndices();
    }
    onCommitAsync() {
      this.updateIndices();
    }
  }
  return CoreDependencyStoreMixin2;
}) {
};

// ../Scheduler/lib/Scheduler/data/DependencyStore.js
var EngineMixin8 = PartOfProject_default(CoreDependencyStoreMixin.derive(AjaxStore));
var DependencyStore = class extends DependencyStoreMixin_default(EngineMixin8.derive(AjaxStore)) {
  /**
   * @hideconfigs lazyLoad
   */
  /**
   * @hideproperties lazyLoad
   */
  /**
   * @hideevents lazyLoadStarted, lazyLoadEnded
   */
  static get defaultConfig() {
    return {
      modelClass: DependencyModel
    };
  }
};
DependencyStore._$name = "DependencyStore";

// ../Scheduler/lib/Scheduler/crud/mixin/AbstractCrudManagerValidation.js
var AbstractCrudManagerValidation_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    static get configurable() {
      return {
        /**
         * This config validates the response structure for requests made by the Crud Manager.
         * When `true`, the Crud Manager checks every parsed response structure for errors
         * and if the response format is invalid, a warning is logged to the browser console.
         *
         * The config is intended to help developers implementing backend integration.
         *
         * @config {Boolean}
         * @default
         * @category CRUD
         */
        validateResponse: true,
        /**
         * When `true` treats parsed responses without `success` property as successful.
         * In this mode a parsed response is treated as invalid if it has explicitly set `success : false`.
         * @config {Boolean}
         * @default
         * @category CRUD
         */
        skipSuccessProperty: true,
        crudLoadValidationWarningPrefix: "CrudManager load response error(s):",
        crudSyncValidationWarningPrefix: "CrudManager sync response error(s):",
        supportShortSyncResponseNote: 'Note: Please consider enabling "supportShortSyncResponse" option to allow less detailed sync responses (https://bryntum.com/products/scheduler/docs/api/Scheduler/crud/AbstractCrudManagerMixin#config-supportShortSyncResponse)',
        disableValidationNote: 'Note: To disable this validation please set the "validateResponse" config to false'
      };
    }
    get crudLoadValidationMandatoryStores() {
      return [];
    }
    getStoreLoadResponseWarnings(storeInfo, responded, expectedResponse) {
      const messages = [], { storeId } = storeInfo, mandatoryStores = this.crudLoadValidationMandatoryStores, result = { [storeId]: {} };
      if (responded) {
        if (!responded.rows) {
          messages.push(`- "${storeId}" store section should have a "rows" property with an array of the store records.`);
          result[storeId].rows = ["..."];
        }
      } else if (mandatoryStores == null ? void 0 : mandatoryStores.includes(storeId)) {
        messages.push(`- No "${storeId}" store section found. It should contain the store data.`);
        result[storeId].rows = ["..."];
      }
      if (messages.length) {
        Object.assign(expectedResponse, result);
      }
      return messages;
    }
    getLoadResponseWarnings(response, options) {
      const messages = [], expectedResponse = {};
      if (!this.skipSuccessProperty) {
        expectedResponse.success = true;
      }
      this.forEachCrudStore((store, storeId, storeInfo) => {
        var _a6;
        if (!((_a6 = options == null ? void 0 : options.stores) == null ? void 0 : _a6.length) || options.stores.includes(storeId)) {
          messages.push(...this.getStoreLoadResponseWarnings(storeInfo, response == null ? void 0 : response[storeId], expectedResponse));
        }
      });
      if (messages.length) {
        messages.push("Please adjust your response to look like this:\n" + JSON.stringify(expectedResponse, null, 4).replace(/"\.\.\."/g, "..."));
        messages.push(this.disableValidationNote);
      }
      return messages;
    }
    validateLoadResponse(response, options) {
      const messages = this.getLoadResponseWarnings(response, options);
      if (messages.length) {
        console.warn(this.crudLoadValidationWarningPrefix + "\n" + messages.join("\n"));
      }
    }
    getStoreSyncResponseWarnings(storeInfo, requested, responded, expectedResponse) {
      const messages = [], missingRows = [], missingRemoved = [], { storeId } = storeInfo, result = { [storeId]: {} }, phantomIdField = storeInfo.phantomIdField || this.phantomIdField, { modelClass } = storeInfo.store, { idField } = modelClass, respondedRows = (responded == null ? void 0 : responded.rows) || [], respondedRemoved = (responded == null ? void 0 : responded.removed) || [];
      let showSupportShortSyncResponseNote = false;
      if (requested == null ? void 0 : requested.added) {
        missingRows.push(
          ...requested.added.filter((record) => {
            return !respondedRows.find((row) => row[phantomIdField] == record[phantomIdField]) && !respondedRemoved.find((row) => row[phantomIdField] == record[phantomIdField] || row[idField] == record[phantomIdField]);
          }).map((record) => ({ [phantomIdField]: record[phantomIdField], [idField]: "..." }))
        );
        if (missingRows.length) {
          const missingIds = missingRows.map((row) => "#" + row[phantomIdField]).join(", ");
          messages.push(`- "${storeId}" store "rows" section should mention added record(s) ${missingIds} sent in the request. It should contain the added records identifiers (both phantom and "real" ones assigned by the backend).`);
        }
      }
      if (this.supportShortSyncResponse) {
        if (!missingRows.length && responded) {
          if (typeof responded !== "object" || Array.isArray(responded)) {
            messages.push(`- "${storeId}" store section should be an Object.`);
            result[storeId]["..."] = "...";
          }
          if (responded.rows && !Array.isArray(responded.rows)) {
            messages.push(`- "${storeId}" store "rows" section should be an array`);
            missingRows.push("...");
          }
          if (responded.removed && !Array.isArray(responded.removed)) {
            messages.push(`- "${storeId}" store "removed" section should be an array:`);
            missingRemoved.push("...");
          }
        }
      } else {
        if (requested == null ? void 0 : requested.updated) {
          const missingUpdatedRows = requested.updated.filter((record) => !respondedRows.find((row) => row[idField] == record[idField])).map((record) => ({ [idField]: record[idField] }));
          missingRows.push(...missingUpdatedRows);
          if (missingUpdatedRows.length) {
            const missingIds = missingUpdatedRows.map((row) => "#" + row[idField]).join(", ");
            messages.push(`- "${storeId}" store "rows" section should mention updated record(s) ${missingIds} sent in the request. It should contain the updated record identifiers.`);
            showSupportShortSyncResponseNote = true;
          }
        }
        if (missingRows.length) {
          missingRows.push("...");
        }
        if (requested == null ? void 0 : requested.removed) {
          missingRemoved.push(
            ...requested.removed.filter((record) => !respondedRows.find((row) => row[idField] == record[idField])).map((record) => ({ [idField]: record[idField] }))
          );
          if (missingRemoved.length) {
            const missingIds = missingRemoved.map((row) => "#" + row[idField]).join(", ");
            messages.push(`- "${storeId}" store "removed" section should mention removed record(s) ${missingIds} sent in the request. It should contain the removed record identifiers.`);
            result[storeId].removed = missingRemoved;
            missingRemoved.push("...");
            showSupportShortSyncResponseNote = true;
          }
        }
      }
      if (missingRows.length) {
        result[storeId].rows = missingRows;
      }
      if (!messages.length) {
        delete result[storeId];
      }
      Object.assign(expectedResponse, result);
      return { messages, showSupportShortSyncResponseNote };
    }
    getSyncResponseWarnings(response, requestDesc) {
      const messages = [], expectedResponse = {}, request = requestDesc.pack;
      if (!this.skipSuccessProperty) {
        expectedResponse.success = true;
      }
      let showSupportShortSyncResponseNote = false;
      this.forEachCrudStore((store, storeId, storeInfo) => {
        const warnings = this.getStoreSyncResponseWarnings(storeInfo, request == null ? void 0 : request[storeId], response[storeId], expectedResponse);
        showSupportShortSyncResponseNote = showSupportShortSyncResponseNote || warnings.showSupportShortSyncResponseNote;
        messages.push(...warnings.messages);
      });
      if (messages.length) {
        messages.push("Please adjust your response to look like this:\n" + JSON.stringify(expectedResponse, null, 4).replace(/"\.\.\.":\s*"\.\.\."/g, ",,,").replace(/"\.\.\."/g, "..."));
        if (showSupportShortSyncResponseNote) {
          messages.push(this.supportShortSyncResponseNote);
        }
        messages.push(this.disableValidationNote);
      }
      return messages;
    }
    validateSyncResponse(response, request) {
      const messages = this.getSyncResponseWarnings(response, request);
      if (messages.length) {
        console.warn(this.crudSyncValidationWarningPrefix + "\n" + messages.join("\n"));
      }
    }
  }, __publicField(_a5, "$name", "AbstractCrudManagerValidation"), _a5;
};

// ../Scheduler/lib/Scheduler/crud/AbstractCrudManagerMixin.js
var AbstractCrudManagerError = class extends Error {
};
var CrudManagerRequestError = class extends AbstractCrudManagerError {
  constructor(cfg = {}) {
    var _a5, _b2;
    super(cfg.message || cfg.request && StringHelper.capitalize((_a5 = cfg.request) == null ? void 0 : _a5.type) + " failed" || "Crud Manager request failed");
    Object.assign(this, cfg);
    this.action = (_b2 = this.request) == null ? void 0 : _b2.type;
  }
};
var storeSortFn = function(lhs, rhs, sortProperty) {
  if (lhs.store) {
    lhs = lhs.store;
  }
  if (rhs.store) {
    rhs = rhs.store;
  }
  lhs = lhs[sortProperty] || 0;
  rhs = rhs[sortProperty] || 0;
  return lhs < rhs ? -1 : lhs > rhs ? 1 : 0;
};
var storeLoadSortFn = function(lhs, rhs) {
  return storeSortFn(lhs, rhs, "loadPriority");
};
var storeSyncSortFn = function(lhs, rhs) {
  return storeSortFn(lhs, rhs, "syncPriority");
};
var AbstractCrudManagerMixin_default = (Target) => {
  var _a5;
  Target.$$meta = Target.$meta;
  const mixins = [];
  if (!Target.isEvents) {
    mixins.push(Events_default);
  }
  if (!Target.isDelayable) {
    mixins.push(Delayable_default);
  }
  mixins.push(AbstractCrudManagerValidation_default);
  return _a5 = class extends (Target || Base).mixin(...mixins) {
    //region Default config
    static get defaultConfig() {
      return {
        /**
         * The server revision stamp.
         * The _revision stamp_ is a number which should be incremented after each server-side change.
         * This property reflects the current version of the data retrieved from the server and gets updated
         * after each {@link #function-load} and {@link #function-sync} call.
         * @property {Number}
         * @readonly
         * @category CRUD
         */
        crudRevision: null,
        /**
         * A list of registered stores whose server communication will be collected into a single batch.
         * Each store is represented by a _store descriptor_.
         * @member {CrudManagerStoreDescriptor[]} crudStores
         * @category CRUD
         */
        /**
         * Sets the list of stores controlled by the CRUD manager.
         *
         * When adding a store to the CrudManager, make sure the server response format is correct for `load`
         * and `sync` requests. Learn more in the
         * [Working with data](#Scheduler/guides/data/crud_manager_in_depth.md#loading-data) guide.
         *
         * Store can be provided by itself, its id or as a _store descriptor_.
         * @config {Core.data.Store[]|String[]|CrudManagerStoreDescriptor[]|StoreConfig[]}
         * @category CRUD
         */
        crudStores: [],
        /**
         * Name of a store property to retrieve store identifiers from. Make sure you have an instance of a
         * store to use it by id. Store identifier is used as a container name holding corresponding store data
         * while transferring them to/from the server. By default, `id` property is used. And in case a
         * container identifier has to differ this config can be used:
         *
         * ```javascript
         * class CatStore extends Store {
         *     static configurable = {
         *         // store id is "meow" but for sending/receiving store data
         *         // we want to have "cats" container in JSON, so we create a new property "storeIdForCrud"
         *         id             : 'meow',
         *         storeIdForCrud : 'cats'
         *     }
         * });
         *
         * // create an instance to use a store by id
         * new CatStore();
         *
         * class MyCrudManager extends CrudManager {
         *     ...
         *     crudStores           : ['meow'],
         *     // crud manager will get store identifier from "storeIdForCrud" property
         *     storeIdProperty  : 'storeIdForCrud'
         * });
         * ```
         * The `storeIdProperty` property can also be specified directly on a store:
         *
         * ```javascript
         * class CatStore extends Store {
         *     static configurable = {
         *         // storeId is "meow" but for sending/receiving store data
         *         // we want to have "cats" container in JSON
         *         id              : 'meow',
         *         // so we create a new property "storeIdForCrud"..
         *         storeIdForCrud  : 'cats',
         *         // and point CrudManager to use it as the store identifier source
         *         storeIdProperty  : 'storeIdForCrud'
         *     }
         * });
         *
         * class DogStore extends Store {
         *     static configurable = {
         *         // storeId is "dogs" and it will be used as a container name for the store data
         *         storeId : 'dogs',
         *         // id is set to get a store by identifier
         *         id      : 'dogs'
         *     }
         * });
         *
         * // create an instance to use a store by id
         * new CatStore();
         * new DogStore();
         *
         * class MyCrudManager extends CrudManager {
         *     ...
         *     crudStores : ['meow', 'dogs']
         * });
         * ```
         * @config {String}
         * @category CRUD
         */
        storeIdProperty: "id",
        crudFilterParam: "filter",
        /**
         * Sends request to the server.
         * @function sendRequest
         * @param {Object} request The request to send. An object having following properties:
         * @param {'load'|'sync'} request.type Request type, can be either `load` or `sync`
         * @param {String} request.data {@link #function-encode Encoded} request.
         * @param {Function} request.success Callback to be started on successful request transferring
         * @param {Function} request.failure Callback to be started on request transfer failure
         * @param {Object} request.thisObj `this` reference for the above `success` and `failure` callbacks
         * @returns {Promise} The request promise.
         * @abstract
         */
        /**
         * Cancels request to the server.
         * @function cancelRequest
         * @param {Promise} promise The request promise to cancel (a value returned by corresponding
         * {@link #function-sendRequest} call).
         * @param {Function} reject Reject handle of the corresponding promise
         * @abstract
         */
        /**
         * Encodes request to the server.
         * @function encode
         * @param {Object} request The request to encode.
         * @returns {String} The encoded request.
         * @abstract
         */
        /**
         * Decodes response from the server.
         * @function decode
         * @param {String} response The response to decode.
         * @returns {Object} The decoded response.
         * @abstract
         */
        transport: {},
        /**
         * When `true` forces the CRUD manager to process responses depending on their `type` attribute.
         * So `load` request may be responded with `sync` response for example.
         * Can be used for smart server logic allowing the server to decide when it's better to respond with a
         * complete data set (`load` response) or it's enough to return just a delta (`sync` response).
         * @config {Boolean}
         * @default
         * @category CRUD
         */
        trackResponseType: false,
        /**
         * When `true` the Crud Manager does not require all updated and removed records to be mentioned in the
         * *sync* response. In this case response should include only server side changes.
         *
         * **Please note that added records should still be mentioned in response to provide real identifier
         * instead of the phantom one.**
         * @config {Boolean}
         * @default
         * @category CRUD
         */
        supportShortSyncResponse: true,
        /**
         * Field name to be used to transfer a phantom record identifier.
         * @config {String}
         * @default
         * @category CRUD
         */
        phantomIdField: "$PhantomId",
        /**
         * Field name to be used to transfer a phantom parent record identifier.
         * @config {String}
         * @default
         * @category CRUD
         */
        phantomParentIdField: "$PhantomParentId",
        /**
         * Specify `true` to automatically call {@link #function-load} method on the next frame after creation.
         *
         * Called on the next frame to allow a Scheduler (or similar) linked to a standalone CrudManager to
         * register its stores before loading starts.
         *
         * @config {Boolean}
         * @default
         * @category CRUD
         */
        autoLoad: false,
        /**
         * The timeout in milliseconds to wait before persisting changes to the server.
         * Used when {@link #config-autoSync} is set to `true`.
         * @config {Number}
         * @default
         * @category CRUD
         */
        autoSyncTimeout: 100,
        /**
         * `true` to automatically persist store changes after edits are made in any of the stores monitored.
         * Please note that sync request will not be invoked immediately but only after
         * {@link #config-autoSyncTimeout} interval.
         * @config {Boolean}
         * @default
         * @category CRUD
         */
        autoSync: false,
        /**
         * `True` to reset identifiers (defined by `idField` config) of phantom records before submitting them
         * to the server.
         * @config {Boolean}
         * @default
         * @category CRUD
         */
        resetIdsBeforeSync: true,
        /**
         * @member {CrudManagerStoreDescriptor[]} syncApplySequence
         * An array of stores presenting an alternative sync responses apply order.
         * Each store is represented by a _store descriptor_.
         * @typings {String[]|CrudManagerStoreDescriptor[]}
         * @category CRUD
         */
        /**
         * An array of store identifiers sets an alternative sync responses apply order.
         * By default, the order in which sync responses are applied to the stores is the same as they
         * registered in. But in case of some tricky dependencies between stores this order can be changed:
         *
         * ```javascript
         * class MyCrudManager extends CrudManager {
         *     // register stores (will be loaded in this order: 'store1' then 'store2' and finally 'store3')
         *     crudStores : ['store1', 'store2', 'store3'],
         *     // but we apply changes from server to them in an opposite order
         *     syncApplySequence : ['store3', 'store2', 'store1']
         * });
         * ```
         *
         * @config {String[]}
         * @typings {String[]|CrudManagerStoreDescriptor[]}
         * @category CRUD
         */
        syncApplySequence: [],
        orderedCrudStores: [],
        /**
         * `true` to write all fields from the record to the server.
         * If set to `false` it will only send the fields that were modified.
         * Note that any fields that have {@link Core/data/field/DataField#config-persist} set to `false` will
         * still be ignored and fields having {@link Core/data/field/DataField#config-alwaysWrite} set to `true`
         * will always be included.
         * @config {Boolean}
         * @default
         * @category CRUD
         */
        writeAllFields: false,
        crudIgnoreUpdates: 0,
        autoSyncSuspendCounter: 0,
        // Flag that shows if crud manager performed successful load request
        crudLoaded: false,
        applyingLoadResponse: false,
        applyingSyncResponse: false,
        callOnFunctions: true
      };
    }
    get isCrudManager() {
      return true;
    }
    //endregion
    //region Init
    construct(config = {}) {
      this._requestId = 0;
      this.activeRequests = {};
      this.crudStoresIndex = {};
      super.construct(config);
    }
    afterConstruct() {
      super.afterConstruct();
      if (this.autoLoad) {
        this._autoLoadPromise = this.doAutoLoad();
      }
    }
    //endregion
    //region Configs
    get loadUrl() {
      var _a6, _b2;
      return (_b2 = (_a6 = this.transport) == null ? void 0 : _a6.load) == null ? void 0 : _b2.url;
    }
    updateLoadUrl(url) {
      ObjectHelper.setPath(this, "transport.load.url", url);
    }
    get syncUrl() {
      var _a6, _b2;
      return (_b2 = (_a6 = this.transport) == null ? void 0 : _a6.sync) == null ? void 0 : _b2.url;
    }
    updateSyncUrl(url) {
      ObjectHelper.setPath(this, "transport.sync.url", url);
    }
    get shouldIgnoreRemoteChangesInSTM() {
      return this.ignoreRemoteChangesInSTM;
    }
    //endregion
    //region Store descriptors & index
    /**
     * Returns a registered store descriptor.
     * @param {String|Core.data.Store} storeId The store identifier or registered store instance.
     * @returns {CrudManagerStoreDescriptor} The descriptor of the store.
     * @category CRUD
     */
    getStoreDescriptor(storeId) {
      if (!storeId) {
        return null;
      }
      if (storeId instanceof Store) {
        storeId = storeId._crudManagerId || storeId.id;
      } else if (typeof storeId === "object") {
        storeId = storeId.storeId;
      }
      return this.crudStoresIndex[storeId];
    }
    fillStoreDescriptor(descriptor) {
      const { store } = descriptor, {
        storeIdProperty = this.storeIdProperty,
        modelClass
      } = store;
      if (!descriptor.storeId) {
        descriptor.storeId = store[storeIdProperty] || store.id;
      }
      if (!descriptor.idField) {
        descriptor.idField = modelClass.idField;
      }
      if (!descriptor.phantomIdField) {
        descriptor.phantomIdField = modelClass.phantomIdField;
      }
      if (!descriptor.phantomParentIdField) {
        descriptor.phantomParentIdField = modelClass.phantomParentIdField;
      }
      if (!("writeAllFields" in descriptor)) {
        descriptor.writeAllFields = store.writeAllFields;
      }
      return descriptor;
    }
    updateCrudStoreIndex() {
      const crudStoresIndex = this.crudStoresIndex = {};
      this.crudStores.forEach((store) => store.storeId && (crudStoresIndex[store.storeId] = store));
    }
    //endregion
    //region Store collection (add, remove, get & iterate)
    /**
     * Returns a registered store.
     * @param {String} storeId Store identifier.
     * @returns {Core.data.Store} Found store instance.
     * @category CRUD
     */
    getCrudStore(storeId) {
      const storeDescriptor = this.getStoreDescriptor(storeId);
      return storeDescriptor == null ? void 0 : storeDescriptor.store;
    }
    forEachCrudStore(fn2, thisObj = this) {
      if (!fn2) {
        throw new Error("Iterator function must be provided");
      }
      this.crudStores.every(
        (store) => fn2.call(thisObj, store.store, store.storeId, store) !== false
      );
    }
    set crudStores(stores2) {
      this._crudStores = [];
      this.addCrudStore(stores2);
      for (const store of this._crudStores) {
        store.loadPriority = store.syncPriority = 0;
      }
    }
    get crudStores() {
      return this._crudStores;
    }
    get orderedCrudStores() {
      return this._orderedCrudStores;
    }
    set orderedCrudStores(stores2) {
      return this._orderedCrudStores = stores2;
    }
    set syncApplySequence(stores2) {
      this._syncApplySequence = [];
      this.addStoreToApplySequence(stores2);
    }
    get syncApplySequence() {
      return this._syncApplySequence;
    }
    internalAddCrudStore(store) {
      const me = this;
      let storeDescriptor;
      if (store instanceof Store) {
        storeDescriptor = { store };
      } else if (typeof store === "object") {
        storeDescriptor = store.store ? store : { store: new Store(store) };
      } else {
        storeDescriptor = { store: Store.getStore(store) };
      }
      me.fillStoreDescriptor(storeDescriptor);
      store = storeDescriptor.store;
      if (store.setCrudManager) {
        store.setCrudManager(me, storeDescriptor);
      }
      store.crudManager = me;
      store._crudManagerId = storeDescriptor.storeId;
      if (store.isAjaxStore) {
        store.pageSize = null;
      }
      if (me.loadUrl || me.syncUrl) {
        store.autoCommit = false;
        store.autoLoad = false;
        if (store.createUrl || store.updateUrl || store.deleteUrl || store.readUrl) {
          console.warn("You have configured an URL on a Store that is handled by a CrudManager that is also configured with an URL. The Store URL's should be removed.");
        }
      }
      me.bindCrudStoreListeners(store);
      return storeDescriptor;
    }
    /**
     * Adds a store to the collection.
     *
     * ```javascript
     * // append stores to the end of collection
     * crudManager.addCrudStore([
     *     store1,
     *     // storeId
     *     'bar',
     *     // store descriptor
     *     {
     *         storeId : 'foo',
     *         store   : store3
     *     },
     *     {
     *         storeId         : 'bar',
     *         store           : store4,
     *         // to write all fields of modified records
     *         writeAllFields  : true
     *     }
     * ]);
     * ```
     *
     * **Note:** Order in which stores are kept in the collection is very essential sometimes.
     * Exactly in this order the loaded data will be put into each store.
     *
     * When adding a store to the CrudManager, make sure the server response format is correct for `load` and `sync`
     * requests. Learn more in the [Working with data](#Scheduler/guides/data/crud_manager_in_depth.md#loading-data) guide.
     *
     * @param {Core.data.Store|String|CrudManagerStoreDescriptor|Core.data.Store[]|String[]|CrudManagerStoreDescriptor[]} store
     * A store or list of stores. Each store might be specified by its instance, `storeId` or _descriptor_.
     * @param {Number} [position] The relative position of the store. If `fromStore` is specified the position
     * will be taken relative to it. If not specified then store(s) will be appended to the end of collection.
     * Otherwise, it will be just a position in stores collection.
     *
     * ```javascript
     * // insert stores store4, store5 to the start of collection
     * crudManager.addCrudStore([ store4, store5 ], 0);
     * ```
     *
     * @param {String|Core.data.Store|CrudManagerStoreDescriptor} [fromStore] The store relative to which position
     * should be calculated. Can be defined as a store identifier, instance or descriptor (the result of
     * {@link #function-getStoreDescriptor} call).
     *
     * ```javascript
     * // insert store6 just before a store having storeId equal to 'foo'
     * crudManager.addCrudStore(store6, 0, 'foo');
     *
     * // insert store7 just after store3 store
     * crudManager.addCrudStore(store7, 1, store3);
     * ```
     * @category CRUD
     */
    addCrudStore(store, position, fromStore) {
      var _a6;
      store = ArrayHelper.asArray(store);
      if (!(store == null ? void 0 : store.length)) {
        return;
      }
      const me = this, stores2 = store.map(me.internalAddCrudStore, me);
      if (typeof position === "undefined") {
        me.crudStores.push(...stores2);
      } else {
        if (fromStore) {
          if (fromStore instanceof Store || typeof fromStore !== "object")
            fromStore = me.getStoreDescriptor(fromStore);
          position += me.crudStores.indexOf(fromStore);
        }
        me.crudStores.splice(position, 0, ...stores2);
      }
      me.orderedCrudStores.push(...stores2);
      me.updateCrudStoreIndex();
      (_a6 = me.afterAddStore) == null ? void 0 : _a6.call(me, stores2);
    }
    // Adds configured scheduler stores to the store collection ensuring correct order
    // unless they're already registered.
    addPrioritizedStore(store) {
      const me = this;
      if (!me.hasCrudStore(store)) {
        me.addCrudStore(store, ArrayHelper.findInsertionIndex(store, me.crudStores, storeLoadSortFn));
      }
      if (!me.hasApplySequenceStore(store)) {
        me.addStoreToApplySequence(store, ArrayHelper.findInsertionIndex(store, me.syncApplySequence, storeSyncSortFn));
      }
    }
    hasCrudStore(store) {
      var _a6;
      return (_a6 = this.crudStores) == null ? void 0 : _a6.some((s) => s === store || s.store === store || s.storeId === store);
    }
    /**
     * Removes a store from collection. If the store was registered in alternative sync sequence list
     * it will be removed from there as well.
     *
     * ```javascript
     * // remove store having storeId equal to "foo"
     * crudManager.removeCrudStore("foo");
     *
     * // remove store3
     * crudManager.removeCrudStore(store3);
     * ```
     *
     * @param {CrudManagerStoreDescriptor|String|Core.data.Store} store The store to remove. Either the store
     * descriptor, store identifier or store itself.
     * @category CRUD
     */
    removeCrudStore(store) {
      const me = this, {
        crudStores,
        orderedCrudStores
      } = me, foundStore = me.getStoreDescriptor(store);
      if (foundStore) {
        me.unbindCrudStoreListeners(foundStore.store);
        ArrayHelper.remove(crudStores, foundStore);
        ArrayHelper.remove(orderedCrudStores, foundStore);
        me.removeStoreFromApplySequence(foundStore);
        delete me.crudStoresIndex[foundStore.storeId];
      } else {
        throw new Error("Store not found in stores collection");
      }
    }
    //endregion
    //region Store listeners
    bindCrudStoreListeners(store) {
      store.ion({
        name: store.id,
        // When a tentatively added record gets confirmed as permanent, this signals a change
        addConfirmed: "onCrudStoreChange",
        change: "onCrudStoreChange",
        destroy: "onCrudStoreDestroy",
        thisObj: this
      });
    }
    unbindCrudStoreListeners(store) {
      this.detachListeners(store.id);
    }
    //endregion
    //region Apply sequence
    /**
     * Adds a store to the alternative sync responses apply sequence.
     * By default, the order in which sync responses are applied to the stores is the same as they registered in.
     * But this order can be changes either on construction step using {@link #config-syncApplySequence} option
     * or by calling this method.
     *
     * **Please note**, that if the sequence was not initialized before this method call then
     * you will have to do it yourself like this for example:
     *
     * ```javascript
     * // alternative sequence was not set for this crud manager
     * // so let's fill it with existing stores keeping the same order
     * crudManager.addStoreToApplySequence(crudManager.crudStores);
     *
     * // and now we can add our new store
     *
     * // we will load its data last
     * crudManager.addCrudStore(someNewStore);
     * // but changes to it will be applied first
     * crudManager.addStoreToApplySequence(someNewStore, 0);
     * ```
     * add registered stores to the sequence along with the store(s) you want to add
     *
     * @param {Core.data.Store|CrudManagerStoreDescriptor|Core.data.Store[]|CrudManagerStoreDescriptor[]} store The
     * store to add or its _descriptor_ (or array of stores or descriptors).
     * @param {Number} [position] The relative position of the store. If `fromStore` is specified the position
     * will be taken relative to it. If not specified then store(s) will be appended to the end of collection.
     * Otherwise, it will be just a position in stores collection.
     *
     * ```javascript
     * // insert stores store4, store5 to the start of sequence
     * crudManager.addStoreToApplySequence([ store4, store5 ], 0);
     * ```
     * @param {String|Core.data.Store|CrudManagerStoreDescriptor} [fromStore] The store relative to which position
     * should be calculated. Can be defined as a store identifier, instance or its descriptor (the result of
     * {@link #function-getStoreDescriptor} call).
     *
     * ```javascript
     * // insert store6 just before a store having storeId equal to 'foo'
     * crudManager.addStoreToApplySequence(store6, 0, 'foo');
     *
     * // insert store7 just after store3 store
     * crudManager.addStoreToApplySequence(store7, 1, store3);
     * ```
     * @category CRUD
     */
    addStoreToApplySequence(store, position, fromStore) {
      if (!store) {
        return;
      }
      store = ArrayHelper.asArray(store);
      const me = this, data = store.reduce((collection, store2) => {
        const s = me.getStoreDescriptor(store2);
        s && collection.push(s);
        return collection;
      }, []);
      if (typeof position === "undefined") {
        me.syncApplySequence.push(...data);
      } else {
        let pos = position;
        if (fromStore) {
          if (fromStore instanceof Store || typeof fromStore !== "object")
            fromStore = me.getStoreDescriptor(fromStore);
          pos += me.syncApplySequence.indexOf(fromStore);
        }
        me.syncApplySequence.splice(pos, 0, ...data);
      }
      const sequenceKeys = me.syncApplySequence.map(({ storeId }) => storeId);
      me.orderedCrudStores = [...me.syncApplySequence];
      me.crudStores.forEach((storeDesc) => {
        if (!sequenceKeys.includes(storeDesc.storeId)) {
          me.orderedCrudStores.push(storeDesc);
        }
      });
    }
    /**
     * Removes a store from the alternative sync sequence.
     *
     * ```javascript
     * // remove store having storeId equal to "foo"
     * crudManager.removeStoreFromApplySequence("foo");
     * ```
     *
     * @param {CrudManagerStoreDescriptor|String|Core.data.Store} store The store to remove. Either the store
     * descriptor, store identifier or store itself.
     * @category CRUD
     */
    removeStoreFromApplySequence(store) {
      const { syncApplySequence } = this, foundStore = this.getStoreDescriptor(store);
      if (syncApplySequence && foundStore) {
        ArrayHelper.remove(syncApplySequence, foundStore);
      }
    }
    hasApplySequenceStore(store) {
      return this.syncApplySequence.some((s) => s === store || s.store === store || s.storeId === store);
    }
    //endregion
    //region Events
    // Remove stores that are destroyed, to not try and apply response changes etc. to them
    onCrudStoreDestroy({ source: store }) {
      this.removeCrudStore(store);
    }
    onCrudStoreChange(event) {
      const me = this, store = event == null ? void 0 : event.source;
      if (store) {
        const storeDescriptor = me.getStoreDescriptor(store);
        if (storeDescriptor && !storeDescriptor.idFieldProcessed && store.modelClass.idFieldProcessed) {
          storeDescriptor.idField = store.modelClass.idField;
          storeDescriptor.idFieldProcessed = true;
        }
      }
      if (me.crudIgnoreUpdates) {
        return;
      }
      if (me.crudStoreHasChanges(event == null ? void 0 : event.source)) {
        me.trigger("hasChanges");
        if (me.autoSync) {
          me.scheduleAutoSync();
        }
      } else {
        me.trigger("noChanges");
      }
    }
    /**
     * Suspends automatic sync upon store changes. Can be called multiple times (it uses an internal counter).
     * @category CRUD
     */
    suspendAutoSync() {
      this.autoSyncSuspendCounter++;
      this.forEachCrudStore((store) => store.suspendAutoCommit());
    }
    /**
     * Resumes automatic sync upon store changes. Will schedule a sync if the internal counter is 0.
     * @param {Boolean} [doSync=true] Pass `true` to schedule a sync after resuming (if there are pending
     * changes) and `false` to not persist the changes.
     * @category CRUD
     */
    resumeAutoSync(doSync = true) {
      const me = this;
      me.autoSyncSuspendCounter--;
      me.forEachCrudStore((store) => store.resumeAutoCommit(doSync));
      if (me.autoSyncSuspendCounter <= 0) {
        me.autoSyncSuspendCounter = 0;
        if (doSync && me.autoSync && me.crudStoreHasChanges()) {
          me.scheduleAutoSync();
        }
      }
    }
    get isAutoSyncSuspended() {
      return this.autoSyncSuspendCounter > 0;
    }
    scheduleAutoSync() {
      const me = this;
      if (!me.hasTimeout("autoSync") && !me.isAutoSyncSuspended) {
        me.setTimeout({
          name: "autoSync",
          fn: () => {
            me.sync().catch((error) => {
            });
          },
          delay: me.autoSyncTimeout
        });
      }
    }
    async triggerFailedRequestEvents(request, response, responseText, fetchOptions, rawResponse) {
      const { options, type: requestType } = request;
      this.trigger("requestFail", { requestType, response, responseText, requestOptions: fetchOptions, rawResponse, responseOptions: fetchOptions });
      this.trigger(requestType + "Fail", { response, requestOptions: fetchOptions, responseText, options, rawResponse, responseOptions: fetchOptions });
    }
    async internalOnResponse(request, responseText, fetchOptions, rawResponse) {
      const me = this, response = responseText ? me.decode(responseText) : null, { options, type: requestType } = request;
      if (responseText && !response) {
        console.error("Failed to parse response: " + responseText);
      }
      if (!response || (me.skipSuccessProperty ? response.success === false : !response.success)) {
        me.triggerFailedRequestEvents(request, response, responseText, fetchOptions);
      } else if (me.trigger("beforeResponseApply", { requestType, response }) !== false && me.trigger(`before${StringHelper.capitalize(requestType)}Apply`, { response, options }) !== false) {
        me.crudRevision = response.revision;
        me.afterSyncAttempt(request);
        await me.applyResponse(request, response, options);
        if (me.isDestroyed) {
          return;
        }
        me.trigger("requestDone", { requestType, response, requestOptions: fetchOptions, rawResponse, responseOptions: fetchOptions });
        me.trigger(requestType, { response, requestOptions: fetchOptions, options, rawResponse, responseOptions: fetchOptions });
        if (requestType === "load" || !me.crudStoreHasChanges()) {
          me.trigger("noChanges");
          if (requestType === "load") {
            me.emitCrudStoreEvents(request.pack.stores, "afterRequest");
          }
        }
      }
      return response;
    }
    afterSyncAttempt(request) {
      var _a6;
      for (const value2 of Object.values(request.pack)) {
        (_a6 = value2 == null ? void 0 : value2.added) == null ? void 0 : _a6.forEach((recordData) => recordData.__record__.isBeingMaterialized = false);
      }
    }
    //endregion
    //region Changes tracking
    /**
     * Suspends {@link #event-hasChanges} and {@link #event-noChanges} events.
     * @category CRUD
     */
    suspendChangeTracking() {
      this.crudIgnoreUpdates++;
      this.crudStores.forEach(({ store }) => {
        store.suspendFilterOnUpdate();
        store.suspendSortOnUpdate();
      });
    }
    /**
     * Resumes {@link #event-hasChanges} and {@link #event-noChanges} events. By default, it will check for changes
     * and if there are any, `hasChanges` or `noChanges` event will be triggered.
     * @param {Boolean} [skipChangeCheck]
     * @category CRUD
     */
    resumeChangeTracking(skipChangeCheck) {
      this.crudStores.forEach(({ store }) => {
        store.resumeFilterOnUpdate();
        store.resumeSortOnUpdate();
      });
      if (this.crudIgnoreUpdates && !--this.crudIgnoreUpdates && !skipChangeCheck) {
        this.onCrudStoreChange();
      }
    }
    /**
     * Returns `true` if changes tracking is suspended
     * @property {Boolean}
     * @readonly
     * @category CRUD
     */
    get isChangeTrackingSuspended() {
      return this.crudIgnoreUpdates > 0;
    }
    /**
     * Returns `true` if any of registered stores (or some particular store) has non persisted changes.
     *
     * ```javascript
     * // if we have any unsaved changes
     * if (crudManager.crudStoreHasChanges()) {
     *     // persist them
     *     crudManager.sync();
     * // otherwise
     * } else {
     *     alert("There are no unsaved changes...");
     * }
     * ```
     *
     * @param {String|Core.data.Store} [storeId] The store identifier or store instance to check changes for.
     * If not specified then will check changes for all of the registered stores.
     * @returns {Boolean} `true` if there are not persisted changes.
     * @category CRUD
     */
    crudStoreHasChanges(storeId) {
      return storeId ? this.isCrudStoreDirty(this.getCrudStore(storeId)) : this.crudStores.some((config) => this.isCrudStoreDirty(config.store));
    }
    isCrudStoreDirty(store) {
      return store.hasChanges;
    }
    //endregion
    //region Load
    doAutoLoad() {
      return this.load().catch((error) => {
      });
    }
    emitCrudStoreEvents(stores2, eventName, eventParams) {
      const event = { action: "read" + eventName, ...eventParams };
      for (const store of this.crudStores) {
        if (stores2.includes(store.storeId)) {
          store.store.trigger(eventName, event);
        }
      }
    }
    getLoadPackage(options) {
      const pack = {
        type: "load",
        requestId: this.requestId
      }, stores2 = this.crudStores, optionsCopy = Object.assign({}, options);
      delete optionsCopy.request;
      pack.stores = stores2.map((store) => {
        var _a6, _b2, _c2;
        const opts = optionsCopy == null ? void 0 : optionsCopy[store.storeId], isPaged = store.isPaged || ((_a6 = store.store) == null ? void 0 : _a6.isPaged);
        if (opts || isPaged) {
          const pageSize = (_c2 = store.pageSize) != null ? _c2 : (_b2 = store.store) == null ? void 0 : _b2.pageSize, params = Object.assign({
            storeId: store.storeId,
            page: 1
          }, opts);
          if (pageSize) {
            params.pageSize = pageSize;
          }
          store.currentPage = params.page;
          if (opts) {
            delete optionsCopy[store.storeId];
          }
          return params;
        }
        return store.storeId;
      });
      Object.assign(pack, optionsCopy);
      return pack;
    }
    loadCrudStore(store, data, options) {
      const rows = data == null ? void 0 : data.rows;
      if ((options == null ? void 0 : options.append) || (data == null ? void 0 : data.append)) {
        store.add(rows, false, { clean: true });
      } else {
        store.data = rows;
      }
      store.trigger("load", { data: rows });
    }
    loadDataToCrudStore(storeDesc, data, options) {
      const store = storeDesc.store, rows = data == null ? void 0 : data.rows;
      store.__loading = true;
      if (rows) {
        this.loadCrudStore(store, data, options, storeDesc);
      }
      store.__loading = false;
    }
    /**
     * Loads data to the Crud Manager
     * @param {Object} response A simple object representing the data.
     * The object structure matches the decoded `load` response structure:
     *
     * ```javascript
     * // load static data into crudManager
     * crudManager.loadCrudManagerData({
     *     success   : true,
     *     resources : {
     *         rows : [
     *             { id : 1, name : 'John' },
     *             { id : 2, name : 'Abby' }
     *         ]
     *     }
     * });
     * ```
     * @param {Object} [options] Extra data loading options.
     * @category CRUD
     */
    loadCrudManagerData(response, options = {}) {
      const me = this;
      me.trigger("beforeLoadCrudManagerData");
      me.suspendChangeTracking();
      me.crudStores.forEach((storeDesc) => {
        const storeId = storeDesc.storeId, data = response[storeId];
        if (data) {
          me.loadDataToCrudStore(storeDesc, data, options[storeId]);
        }
      });
      me.resumeChangeTracking(true);
      me.trigger("loadCrudManagerData");
    }
    /**
     * Returns true if the crud manager is currently loading data
     * @property {Boolean}
     * @readonly
     * @category CRUD
     */
    get isCrudManagerLoading() {
      return Boolean(this.activeRequests.load || this.applyingLoadResponse);
    }
    /**
     * Returns true if the crud manager is currently syncing data
     * @property {Boolean}
     * @readonly
     * @category CRUD
     */
    get isCrudManagerSyncing() {
      return Boolean(this.activeRequests.sync || this.applyingSyncResponse);
    }
    get isLoadingOrSyncing() {
      return Boolean(this.isCrudManagerLoading || this.isCrudManagerSyncing);
    }
    /**
     * Loads data to the stores registered in the crud manager. For example:
     *
     * ```javascript
     * crudManager.load(
     *     // here are request parameters
     *     {
     *         store1 : { append : true, page : 3, smth : 'foo' },
     *         store2 : { page : 2, bar : '!!!' }
     *     }
     * ).then(
     *     () => alert('OMG! It works!'),
     *     ({ response, cancelled }) => console.log(`Error: ${cancelled ? 'Cancelled' : response.message}`)
     * );
     * ```
     *
     * ** Note: ** If there is an incomplete load request in progress then system will try to cancel it by calling {@link #function-cancelRequest}.
     * @param {Object|String} [options] The request parameters or a URL.
     * @param {Object} [options.request] An object which contains options to merge
     * into the options which are passed to {@link Scheduler/crud/transport/AjaxTransport#function-sendRequest}.
     * ```javascript
     * {
     *     store1 : { page : 3, append : true, smth : 'foo' },
     *     store2 : { page : 2, bar : '!!!' },
     *     request : {
     *         params : {
     *             startDate : '2021-01-01'
     *         }
     *     }
     * },
     * ```
     *
     * Omitting request arg:
     * ```javascript
     * crudManager.load().then(
     *     () => alert('OMG! It works!'),
     *     ({ response, cancelled }) => console.log(`Error: ${cancelled ? 'Cancelled' : response.message}`)
     * );
     * ```
     *
     * When presented it should be an object where keys are store Ids and values are, in turn, objects
     * of parameters related to the corresponding store. These parameters will be transferred in each
     * store's entry in the `stores` property of the POST data.
     *
     * Additionally, for flat stores `append: true` can be specified to add loaded records to the existing records,
     * default is to remove corresponding store's existing records first.
     * **Please note** that for delta loading you can also use an {@link #config-trackResponseType alternative approach}.
     * @param {'sync'|'load'} [options.request.type] The request type. Either `load` or `sync`.
     * @param {String} [options.request.url] The URL for the request. Overrides the URL defined in the `transport`
     * object
     * @param {String} [options.request.data] The encoded _Crud Manager_ request data.
     * @param {Object} [options.request.params] An object specifying extra HTTP params to send with the request.
     * @param {Function} [options.request.success] A function to be started on successful request transferring.
     * @param {String} [options.request.success.rawResponse] `Response` object returned by the
     * [fetch api](https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API).
     * @param {Function} [options.request.failure] A function to be started on request transfer failure.
     * @param {String} [options.request.failure.rawResponse] `Response` object returned by the
     * [fetch api](https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API).
     * @param {Object} [options.request.thisObj] `this` reference for the above `success` and `failure` functions.
     * @returns {Promise} Promise, which is resolved if request was successful.
     * Both the resolve and reject functions are passed a `state` object. State object has following structure:
     *
     * ```
     * {
     *     cancelled       : Boolean, // **optional** flag, which is present when promise was rejected
     *     rawResponse     : String,  // raw response from ajax request, either response xml or text
     *     rawResponseText : String,  // raw response text as String from ajax request
     *     response        : Object,  // processed response in form of object
     *     options         : Object   // options, passed to load request
     * }
     * ```
     *
     * If promise was rejected by {@link #event-beforeLoad} event, `state` object will have the following structure:
     *
     * ```
     * {
     *     cancelled : true
     * }
     * ```
     * @category CRUD
     * @async
     */
    load(options) {
      if (typeof options === "string") {
        options = {
          request: {
            url: options
          }
        };
      }
      const me = this, pack = me.getLoadPackage(options);
      me._autoLoadPromise = null;
      return new Promise((resolve, reject) => {
        if (me.trigger("beforeLoad", { pack }) !== false) {
          const { load } = me.activeRequests;
          if (load) {
            me.cancelRequest(load.desc, load.reject);
            me.trigger("loadCanceled", { pack });
          }
          const request = Objects.assign({
            id: pack.requestId,
            data: me.encode(pack),
            type: "load",
            success: me.onCrudRequestSuccess,
            failure: me.onCrudRequestFailure,
            thisObj: me
          }, options == null ? void 0 : options.request);
          me.activeRequests.load = {
            type: "load",
            options,
            pack,
            resolve,
            reject(...args) {
              var _a6;
              request.success = request.failure = null;
              if (!((_a6 = args[0]) == null ? void 0 : _a6.cancelled)) {
                reject(...args);
              }
            },
            id: pack.requestId,
            desc: me.sendRequest(request)
          };
          me.emitCrudStoreEvents(pack.stores, "loadStart");
          me.trigger("loadStart", { pack });
        } else {
          me.trigger("loadCanceled", { pack });
          reject({ cancelled: true });
        }
      });
    }
    getActiveCrudManagerRequest(requestType) {
      let request = this.activeRequests[requestType];
      if (!request && this.trackResponseType) {
        request = Object.values(this.activeRequests)[0];
      }
      return request;
    }
    //endregion
    //region Changes (prepare, process, get)
    prepareAddedRecordData(record, storeInfo) {
      const me = this, { store } = storeInfo, { isTree } = store, phantomIdField = storeInfo.phantomIdField || me.phantomIdField, phantomParentIdField = storeInfo.phantomParentIdField || me.phantomParentIdField, subStoreFields = store.modelClass.allFields.filter((field) => field.subStore), cls = record.constructor, data = Object.assign(record.persistableData, {
        [phantomIdField]: record.id
      });
      record.$isPhantom = true;
      if (isTree) {
        const { parent } = record;
        if (parent && !parent.isRoot && parent.isPhantom) {
          data[phantomParentIdField] = parent.id;
        }
      }
      if (me.resetIdsBeforeSync) {
        ObjectHelper.deletePath(data, cls.idField);
      }
      subStoreFields.forEach((field) => {
        const subStore = record.get(field.name);
        if (subStore.totalCount) {
          data[field.dataSource] = {
            added: subStore.getRange().map((record2) => me.prepareAddedRecordData(record2, { store: subStore }))
          };
        }
      });
      Object.defineProperty(data, "__record__", { value: record, enumerable: false });
      return data;
    }
    prepareAdded(list, storeInfo) {
      return list.filter((record) => record.isValid).map((record) => this.prepareAddedRecordData(record, storeInfo));
    }
    prepareUpdated(list, storeInfo) {
      const { store } = storeInfo, { isTree } = store, writeAllFields = storeInfo.writeAllFields || storeInfo.writeAllFields !== false && this.writeAllFields, phantomParentIdField = storeInfo.phantomParentIdField || this.phantomParentIdField, subStoreFields = store.modelClass.allFields.filter((field) => field.subStore);
      if (storeInfo.store.tree) {
        const rootNode = storeInfo.store.rootNode;
        list = list.filter((record) => record !== rootNode);
      }
      return list.filter((record) => record.isValid).reduce((data, record) => {
        let recordData;
        if (writeAllFields) {
          recordData = record.persistableData;
        } else {
          recordData = record.modificationDataToWrite;
        }
        if (isTree) {
          const { parent } = record;
          if (parent && !parent.isRoot && parent.isPhantom) {
            recordData[phantomParentIdField] = parent.id;
          }
        }
        subStoreFields.forEach((field) => {
          const subStore = record.get(field.name);
          recordData[field.dataSource] = this.getCrudStoreChanges({ store: subStore });
        });
        if (!ObjectHelper.isEmpty(recordData)) {
          data.push(recordData);
        }
        return data;
      }, []);
    }
    prepareRemoved(list, { store }) {
      var _a6, _b2;
      let removedRecords;
      if (!this.includeChildrenInRemoveRequest) {
        removedRecords = [];
        for (const record of list) {
          const parentId = (_b2 = record.parentId) != null ? _b2 : (_a6 = record.meta.modified) == null ? void 0 : _a6.parentId;
          if (parentId != null && (parentId === store.rootNode.id || !list.some((r) => r.id === parentId))) {
            removedRecords.push(record);
          }
        }
      } else {
        removedRecords = list;
      }
      return removedRecords.map((record) => ObjectHelper.setPath({}, record.constructor.idField, record.id));
    }
    getCrudStoreChanges(storeDescriptor) {
      var _a6, _b2;
      const { store } = storeDescriptor;
      let { added = [], modified: updated = [], removed = [] } = store.changes || {}, result;
      if (added.length) {
        added = this.prepareAdded(added, storeDescriptor);
      }
      if (updated.length || ((_a6 = store.modifiedMaterialized) == null ? void 0 : _a6.size)) {
        updated = this.prepareUpdated(
          [...updated, ...(_b2 = store.modifiedMaterialized) != null ? _b2 : []],
          storeDescriptor
        );
      }
      if (removed.length) {
        removed = this.prepareRemoved(removed, storeDescriptor);
      }
      if (added.length || updated.length || removed.length) {
        result = {};
        if (added.length)
          result.added = added;
        if (updated.length)
          result.updated = updated;
        if (removed.length)
          result.removed = removed;
      }
      return result;
    }
    getChangesetPackage() {
      const { changes } = this;
      return changes || this.forceSync ? {
        type: "sync",
        requestId: this.requestId,
        revision: this.crudRevision,
        ...changes
      } : null;
    }
    //endregion
    //region Apply
    /**
     * Returns current changes as an object consisting of added/modified/removed arrays of records for every
     * managed store, keyed by each store's `id`. Returns `null` if no changes exist. Format:
     *
     * ```javascript
     * {
     *     resources : {
     *         added    : [{ name : 'New guy' }],
     *         modified : [{ id : 2, name : 'Mike' }],
     *         removed  : [{ id : 3 }]
     *     },
     *     events : {
     *         modified : [{  id : 12, name : 'Cool task' }]
     *     },
     *     ...
     * }
     * ```
     *
     * @property {Object}
     * @readonly
     * @category CRUD
     */
    get changes() {
      const data = {};
      this.crudStores.forEach((store) => {
        const changes = this.getCrudStoreChanges(store);
        if (changes) {
          data[store.storeId] = changes;
        }
      });
      return Object.keys(data).length > 0 ? data : null;
    }
    isFieldValueEqual(field, value2, assertedValue) {
      return (field == null ? void 0 : field.isEqual) ? field.isEqual(value2, assertedValue) : ObjectHelper.isEqual(value2, assertedValue);
    }
    processRecordResponse(storeDescriptor, record, recordResponse, recordRequestPathified, recordResponsePathified, localChanges) {
      const syncConflictResolution = storeDescriptor.syncConflictResolution || this.syncConflictResolution, { fieldDataSourceMap } = storeDescriptor.store.modelClass, {
        idField,
        phantomIdField = this.phantomIdField
      } = storeDescriptor;
      let result = {};
      if (syncConflictResolution === "local") {
        for (const [key, incomingValue] of Object.entries(recordResponsePathified)) {
          let skipField = false;
          const field = fieldDataSourceMap[key];
          if (field && (record || localChanges)) {
            const localValue = record ? record.getFieldPersistentValue(field) : localChanges[key];
            let isValueSent = key in recordRequestPathified, sentValue = recordRequestPathified[key];
            if (key === idField && recordRequestPathified[phantomIdField]) {
              isValueSent = isValueSent || phantomIdField in recordRequestPathified;
              sentValue = recordRequestPathified[phantomIdField];
            }
            skipField = // - local value has changed during the request (its current value !== value sent in the request)
            isValueSent && !this.isFieldValueEqual(field, sentValue, localValue) || // - remote value === current value (ignore meaningless noise)
            key !== idField && this.isFieldValueEqual(field, incomingValue, localValue);
          }
          if (!skipField) {
            ObjectHelper.setPath(result, key, incomingValue);
          }
        }
      } else {
        result = recordResponse;
      }
      return result;
    }
    getRecordChangesToClear(storeDescriptor, recordRequestPathified, recordResponse, localChanges, record = null) {
      const { store } = storeDescriptor, { fieldDataSourceMap } = store.modelClass, parentIdField = store.isTree && store.modelClass.parentIdField;
      let hasChanges = false, result;
      if (localChanges) {
        result = {};
        for (const [key, requestValue] of Object.entries(recordRequestPathified)) {
          let field = fieldDataSourceMap[key];
          if (!field && key === parentIdField) {
            field = { name: "parentId" };
          }
          if (field && key in localChanges && (this.isFieldValueEqual(field, requestValue, localChanges[key]) || recordResponse && this.isFieldValueEqual(field, requestValue, recordResponse[key]))) {
            result[field.name] = record ? record[field.name] : true;
            hasChanges = true;
          }
        }
      }
      return hasChanges ? result : null;
    }
    processStoreResponse(storeDescriptor, storeResponse, storeRequest) {
      const me = this, { supportShortSyncResponse } = me, { idField, store, phantomIdField = me.phantomIdField } = storeDescriptor, {
        addedMap: requestAddedMap,
        updatedMap: requestUpdatedMap,
        removedMap: requestRemovedMap
      } = me.buildStoreSyncRequestMaps(storeDescriptor, storeRequest, storeResponse), responseRowsMap = /* @__PURE__ */ new Map(), clearChanges = /* @__PURE__ */ new Map(), added = [], updated = [], recordIdsToRemove = /* @__PURE__ */ new Set();
      for (const recordResponse of (storeResponse == null ? void 0 : storeResponse.rows) || []) {
        const id2 = ObjectHelper.getPath(recordResponse, idField), phantomId = ObjectHelper.getPath(recordResponse, phantomIdField), recordRequest = requestAddedMap.get(phantomId) || requestUpdatedMap.get(id2), record = store.getById(phantomId || id2) || store.removed.get(phantomId || id2), localChanges = record == null ? void 0 : record.modificationDataToWrite, recordRequestPathified = ObjectHelper.pathifyKeys(recordRequest), recordResponsePathified = ObjectHelper.pathifyKeys(recordResponse), processedRecordResponse = me.processRecordResponse(
          storeDescriptor,
          record,
          recordResponse,
          recordRequestPathified,
          recordResponsePathified,
          localChanges
        );
        if (processedRecordResponse) {
          if (id2) {
            responseRowsMap.set(id2, processedRecordResponse);
          }
          if (phantomId) {
            responseRowsMap.set(phantomId, processedRecordResponse);
          }
          if (record) {
            updated.push(processedRecordResponse);
          } else {
            added.push(processedRecordResponse);
          }
        }
      }
      for (const recordRequest of [...requestAddedMap.values(), ...requestUpdatedMap.values()]) {
        const phantomId = ObjectHelper.getPath(recordRequest, phantomIdField), id2 = ObjectHelper.getPath(recordRequest, idField), record = store.getById(phantomId || id2) || store.removed.get(phantomId || id2), recordResponse = responseRowsMap.get(id2) || responseRowsMap.get(phantomId);
        if (record && // short requests enabled or the record is responded
        (supportShortSyncResponse || responseRowsMap.has(id2) || responseRowsMap.has(phantomId))) {
          clearChanges.set(
            record,
            me.getRecordChangesToClear(
              storeDescriptor,
              ObjectHelper.pathifyKeys(recordRequest),
              recordResponse,
              record.modificationDataToWrite
            )
          );
        }
      }
      for (const recordData of [
        ...(storeResponse == null ? void 0 : storeResponse.removed) || [],
        // w/ short sync responses enabled we commit requested removals on any successful response
        ...me.supportShortSyncResponse ? requestRemovedMap.values() : []
      ]) {
        const id2 = ObjectHelper.getPath(recordData, idField);
        if ((store.getById(id2) || store.removed.includes(id2)) && !responseRowsMap.has(id2)) {
          recordIdsToRemove.add(id2);
        }
      }
      return {
        added,
        updated,
        removed: [...recordIdsToRemove].map((id2) => ({ [idField]: id2 })),
        clearChanges
      };
    }
    buildStoreSyncRequestMaps({ idField, phantomIdField = this.phantomIdField }, storeRequest) {
      const addedMap = /* @__PURE__ */ new Map(), updatedMap = /* @__PURE__ */ new Map(), removedMap = /* @__PURE__ */ new Map();
      if (storeRequest) {
        for (const requestRecord of storeRequest.added || []) {
          const phantomId = ObjectHelper.getPath(requestRecord, phantomIdField);
          addedMap.set(phantomId, requestRecord);
        }
        for (const requestRecord of storeRequest.updated || []) {
          const id2 = ObjectHelper.getPath(requestRecord, idField);
          updatedMap.set(id2, requestRecord);
        }
        for (const requestRecord of storeRequest.removed || []) {
          const id2 = ObjectHelper.getPath(requestRecord, idField);
          removedMap.set(id2, requestRecord);
        }
      }
      return {
        addedMap,
        updatedMap,
        removedMap
      };
    }
    applyChangesToStore(storeDescriptor, storeResponse, storeRequest) {
      var _a6, _b2, _c2;
      const me = this, phantomIdField = storeDescriptor.phantomIdField || me.phantomIdField, { store } = storeDescriptor, idField = store.modelClass.getFieldDataSource("id"), subStoreFields = store.modelClass.allFields.filter((field) => field.subStore), {
        added,
        updated,
        removed,
        clearChanges
      } = me.processStoreResponse(storeDescriptor, storeResponse, storeRequest);
      const extraLogEntries = [];
      if (updated.length && subStoreFields.length) {
        for (const updateData of updated) {
          const record = store.getById((_a6 = updateData[phantomIdField]) != null ? _a6 : updateData[idField]), recordRequest = ((_b2 = storeRequest.added) == null ? void 0 : _b2.find((t) => t[phantomIdField] == updateData[phantomIdField])) || ((_c2 = storeRequest.updated) == null ? void 0 : _c2.find((t) => t[idField] == updateData[idField])), extraLogInfo = {};
          for (const field of subStoreFields) {
            const { dataSource } = field, subStoreDesc = me.fillStoreDescriptor({ store: record.get(field.name) });
            me.applyChangesToStore(
              subStoreDesc,
              updateData[dataSource],
              recordRequest == null ? void 0 : recordRequest[dataSource]
            );
            extraLogInfo[dataSource] = "foo";
            delete updateData[dataSource];
          }
          extraLogEntries.push([record, extraLogInfo]);
        }
      }
      let log;
      if (removed.length || added.length || updated.length) {
        log = store.applyChangeset({ removed, added, updated }, null, phantomIdField, true, true);
      }
      extraLogEntries.forEach(([record, logEntry]) => Object.assign(log.get(record.id), logEntry));
      for (const [id2, changesToClear] of clearChanges) {
        if (changesToClear) {
          const record = id2.isModel ? id2 : store.getById(id2);
          record.internalClearChanges(false, true, changesToClear);
        }
      }
      store.trigger("appliedCrudChangesToStore", { crudManager: me });
      return log;
    }
    applySyncResponse(response, request) {
      var _a6;
      const me = this, {
        orderedCrudStores,
        supportShortSyncResponse
      } = me;
      me.applyingChangeset = me.applyingSyncResponse = true;
      me.suspendChangeTracking();
      for (const storeDescriptor of orderedCrudStores) {
        const storeResponse = response[storeDescriptor.storeId];
        if (storeResponse || supportShortSyncResponse) {
          me.applyChangesToStore(storeDescriptor, storeResponse, (_a6 = request == null ? void 0 : request.pack) == null ? void 0 : _a6[storeDescriptor.storeId]);
        }
      }
      me.resumeChangeTracking(true);
      me.applyingChangeset = me.applyingSyncResponse = false;
    }
    async applyLoadResponse(response, options) {
      this.applyingLoadResponse = true;
      await this.loadCrudManagerData(response, options);
      this.applyingLoadResponse = false;
    }
    async applyResponse(request, response, options) {
      const me = this, responseType = me.trackResponseType && response.type || request.type, { applyingResponse } = me;
      me.applyingResponse = true;
      switch (responseType) {
        case "load":
          if (me.validateResponse) {
            me.validateLoadResponse(response, options);
          }
          await me.applyLoadResponse(response, options);
          break;
        case "sync":
          if (me.validateResponse) {
            me.validateSyncResponse(response, request);
          }
          me.applySyncResponse(response, request);
          break;
      }
      me.applyingResponse = applyingResponse;
    }
    /**
     * Applies a set of changes, as an object keyed by store id, to the affected stores. This function is intended
     * to use in apps that handle their own data syncing, it is not needed when using the CrudManager approach.
     *
     * Example of a changeset:
     * ```javascript
     * project.applyChangeset({
     *     events : {
     *         added : [
     *             { id : 10, name : 'Event 10', startDate : '2022-06-07' }
     *         ],
     *         updated : [
     *             { id : 5, name : 'Changed' }
     *         ],
     *         removed : [
     *             { id : 1 }
     *         ]
     *     },
     *     resources : { ... },
     *     ...
     * });
     * ```
     *
     * Optionally accepts a `transformFn` to convert an incoming changeset to the expected format.
     * See {@link Core/data/Store#function-applyChangeset} for more details.
     *
     * <div class="note">
     * In SchedulerPro and Gantt, when {@link Scheduler/crud/AbstractCrudManagerMixin#config-autoSync} is enabled,
     * and client wants to apply changes without triggering a sync request, <b>applyProjectChanges</b> method
     * should be used instead of this. It helps maintain current behavior while providing flexibility for scenarios
     * where syncing is not required.
     * </div>
     *
     * @param {Object} changes Changeset to apply, an object keyed by store id where each value follows the
     * format described in {@link Core/data/Store#function-applyChangeset}
     * @param {Function} [transformFn] Optional function used to preprocess a changeset per store in a different
     * format, should return an object with the format expected by {@link Core/data/Store#function-applyChangeset}
     * @param {String} [phantomIdField] Field used by the backend when communicating a record being assigned a
     * proper id instead of a phantom id
     */
    applyChangeset(changes, transformFn = null, phantomIdField, logChanges = false) {
      const me = this, log = logChanges ? /* @__PURE__ */ new Map() : void 0;
      me.applyingChangeset = true;
      me.suspendAutoSync();
      me.suspendChangeTracking();
      for (const { store, phantomIdField: phantomIdField2 } of me.orderedCrudStores) {
        if (changes[store.id]) {
          const storeLog = store.applyChangeset(
            changes[store.id],
            transformFn,
            phantomIdField2 || me.phantomIdField,
            // mark this changeset as remote to enforce it
            true,
            logChanges
          );
          if (storeLog) {
            log.set(store.id, storeLog);
          }
        }
      }
      me.applyingChangeset = false;
      me.resumeChangeTracking(true);
      me.resumeAutoSync(false);
      return log;
    }
    //endregion
    /**
     * Generates unique request identifier.
     * @internal
     * @template
     * @returns {Number} The request identifier.
     * @category CRUD
     */
    get requestId() {
      return Number.parseInt(`${Date.now()}${this._requestId++}`);
    }
    /**
     * Persists changes made on the registered stores to the server and/or receives changes made on the backend.
     * Usage:
     *
     * ```javascript
     * // persist and run a callback on request completion
     * crud.sync().then(
     *     () => console.log("Changes saved..."),
     *     ({ response, cancelled }) => console.log(`Error: ${cancelled ? 'Cancelled' : response.message}`)
     * );
     * ```
     *
     * ** Note: ** If there is an incomplete sync request in progress then system will queue the call and delay it
     * until previous request completion.
     * In this case {@link #event-syncDelayed} event will be fired.
     *
     * ** Note: ** Please take a look at {@link #config-autoSync} config. This option allows to persist changes
     * automatically after any data modification.
     *
     * ** Note: ** By default a sync request is only sent if there are any local {@link #property-changes}. To
     * always send a request when calling this function, configure {@link #config-forceSync} as `true`.
     *
     * @returns {Promise} Promise, which is resolved if request was successful.
     * Both the resolve and reject functions are passed a `state` object. State object has the following structure:
     * ```
     * {
     *     cancelled       : Boolean, // **optional** flag, which is present when promise was rejected
     *     rawResponse     : String,  // raw response from ajax request, either response xml or text
     *     rawResponseText : String,  // raw response text as String from ajax request
     *     response        : Object,  // processed response in form of object
     * }
     * ```
     * If promise was rejected by the {@link #event-beforeSync} event, `state` object will have this structure:
     * ```
     * {
     *     cancelled : true
     * }
     * ```
     * @category CRUD
     * @async
     */
    sync() {
      const me = this;
      me.clearTimeout("autoSync");
      if (me.activeRequests.sync) {
        me.trigger("syncDelayed");
        return me.activeSyncPromise = me.activeSyncPromise.finally(() => me.sync());
      }
      return me.activeSyncPromise = new Promise((resolve, reject) => {
        var _a6;
        const pack = me.getChangesetPackage();
        if (!pack) {
          resolve(null);
          return;
        }
        this.forEachCrudStore((store) => {
          var _a7;
          return (_a7 = store.modifiedMaterialized) == null ? void 0 : _a7.clear();
        });
        for (const value2 of Object.values(pack)) {
          (_a6 = value2 == null ? void 0 : value2.added) == null ? void 0 : _a6.forEach((recordData) => recordData.__record__.isBeingMaterialized = true);
        }
        me.activeRequests.sync = {
          type: "sync",
          pack,
          resolve,
          reject,
          // We'll set .desc (the result of me.sendRequest()) only once we know it's not canceled
          desc: null,
          id: pack.requestId
        };
        const beforeSyncResult = me.trigger("beforeSync", { pack });
        Promise.resolve(beforeSyncResult).then((result) => {
          var _a7, _b2;
          if (result !== false) {
            me.trigger("syncStart", { pack });
            if ((_a7 = me.eventStore) == null ? void 0 : _a7.usesSingleAssignment) {
              (_b2 = me.assignmentStore) == null ? void 0 : _b2.acceptChanges();
            }
            me.activeRequests.sync.desc = me.sendRequest({
              id: pack.requestId,
              data: me.encode(pack),
              type: "sync",
              success: me.onCrudRequestSuccess,
              failure: me.onCrudRequestFailure,
              thisObj: me
            });
          } else {
            me.trigger("syncCanceled", { pack });
            me.activeRequests.sync = null;
            reject({ cancelled: true });
          }
        }).catch((error) => {
          me.activeRequests.sync = null;
          reject(error);
        });
      }).catch((error) => {
        if (error && !error.cancelled) {
          throw error;
        }
        return error;
      });
    }
    async onCrudRequestSuccess(rawResponse, fetchOptions, request) {
      const me = this, {
        type: requestType,
        id: requestId
      } = request;
      if (me.isDestroyed)
        return;
      let responseText = "";
      request = me.activeRequests[requestType];
      try {
        responseText = await rawResponse.text();
      } catch (e) {
      }
      if (me.isDestroyed)
        return;
      if ((request == null ? void 0 : request.id) !== requestId) {
        throw new Error(`Interleaved ${requestType} operation detected`);
      }
      me.activeRequests[requestType] = null;
      const response = await me.internalOnResponse(request, responseText, fetchOptions, rawResponse);
      if (me.isDestroyed)
        return;
      if (!response || (me.skipSuccessProperty ? (response == null ? void 0 : response.success) === false : !(response == null ? void 0 : response.success))) {
        const error = {
          rawResponse,
          response,
          request
        };
        if (response == null ? void 0 : response.message) {
          error.message = response.message;
        }
        request.reject(new CrudManagerRequestError(error));
      }
      me["crud" + StringHelper.capitalize(request.type) + "ed"] = true;
      request.resolve({ response, rawResponse, responseText, request });
    }
    async onCrudRequestFailure(rawResponse, fetchOptions, request) {
      var _a6;
      const me = this;
      if (me.isDestroyed)
        return;
      request = me.activeRequests[request.type];
      const signal = (_a6 = fetchOptions == null ? void 0 : fetchOptions.abortController) == null ? void 0 : _a6.signal, wasAborted = Boolean(signal == null ? void 0 : signal.aborted);
      me.afterSyncAttempt(request);
      if (!wasAborted) {
        let response, responseText = "";
        try {
          responseText = await rawResponse.text();
          response = me.decode(responseText);
        } catch (e) {
        }
        if (me.isDestroyed)
          return;
        me.triggerFailedRequestEvents(request, response, responseText, fetchOptions, rawResponse);
        if (me.isDestroyed)
          return;
        request.reject(new CrudManagerRequestError({
          rawResponse,
          request
        }));
      }
      me.activeRequests[request.type] = null;
    }
    /**
     * Accepts all changes in all stores, resets the modification tracking:
     * * Clears change tracking for all records
     * * Clears added
     * * Clears modified
     * * Clears removed
     * Leaves the store in an "unmodified" state.
     * @category CRUD
     */
    acceptChanges() {
      this.crudStores.forEach(({ store }) => store.acceptChanges());
      this.trigger("acceptChanges");
    }
    /**
     * Reverts all changes in all stores and re-inserts any records that were removed locally. Any new uncommitted
     * records will be removed.
     * @category CRUD
     */
    revertChanges() {
      this.revertCrudStoreChanges();
      this.trigger("revertChanges");
    }
    revertCrudStoreChanges() {
      const { usesSingleAssignment } = this.eventStore;
      this.orderedCrudStores.forEach(({ store }) => (!store.isAssignmentStore || !usesSingleAssignment) && store.revertChanges());
    }
    /**
     * Removes all stores and cancels active requests.
     * @category CRUD
     * @internal
     */
    doDestroy() {
      const me = this, { load, sync } = me.activeRequests;
      load && me.cancelRequest(load.desc, load.reject);
      sync && me.cancelRequest(sync.desc, sync.reject);
      while (me.crudStores.length > 0) {
        me.removeCrudStore(me.crudStores[0]);
      }
      super.doDestroy && super.doDestroy();
    }
    /**
     * Returns the data from all CrudManager `crudStores` in a format that can be consumed by `inlineData`.
     * @internal
     */
    get crudStoresJSON() {
      var _a6;
      const result = {};
      this.forEachCrudStore((store, storeId) => result[`${storeId}Data`] = store.toJSON());
      if ((_a6 = this.assignmentStore) == null ? void 0 : _a6.usesSingleAssignment) {
        delete result.assignmentsData;
      }
      return result;
    }
    set crudStoresJSON(data) {
      this.forEachCrudStore((store, storeId) => {
        const dataName = `${storeId}Data`;
        if (data[dataName]) {
          store.data = data[dataName];
        }
      });
    }
  }, /**
   * Fires before server response gets applied to the stores. Return `false` to prevent data applying.
   * This event can be used for server data preprocessing. To achieve it user can modify the `response` object.
   * @event beforeResponseApply
   * @param {Scheduler.crud.AbstractCrudManager} source The CRUD manager.
   * @param {'sync'|'load'} requestType The request type (`sync` or `load`).
   * @param {Object} response The decoded server response object.
   * @preventable
   */
  /**
   * Fires before loaded data get applied to the stores. Return `false` to prevent data applying.
   * This event can be used for server data preprocessing. To achieve it user can modify the `response` object.
   * @event beforeLoadApply
   * @param {Scheduler.crud.AbstractCrudManager} source The CRUD manager.
   * @param {Object} response The decoded server response object.
   * @param {Object} options Options provided to the {@link #function-load} method.
   * @preventable
   */
  /**
   * Fires before sync response data get applied to the stores. Return `false` to prevent data applying.
   * This event can be used for server data preprocessing. To achieve it user can modify the `response` object.
   * @event beforeSyncApply
   * @param {Scheduler.crud.AbstractCrudManager} source The CRUD manager.
   * @param {Object} response The decoded server response object.
   * @preventable
   */
  __publicField(_a5, "$name", "AbstractCrudManagerMixin"), __publicField(_a5, "configurable", {
    /**
     * Convenience shortcut to set only the url to load from, when you do not need to supply any other config
     * options in the `load` section of the `transport` config.
     *
     * Using `loadUrl`:
     * ```javascript
     * {
     *     loadUrl : 'read.php
     * }
     * ```
     *
     * Equals the following `transport` config:
     * ```javascript
     * {
     *     transport : {
     *         load : {
     *             url : 'read.php'
     *         }
     *     }
     * }
     * ```
     *
     * When read at runtime, it will return the value from `transport.load.url`.
     *
     * @prp {String}
     * @category Common
     */
    loadUrl: null,
    /**
     * Convenience shortcut to set only the url to sync to, when you do not need to supply any other config
     * options in the `sync` section of the `transport` config.
     *
     * Using `loadUrl`:
     * ```javascript
     * {
     *     syncUrl : 'sync.php
     * }
     * ```
     *
     * Equals the following `transport` config:
     * ```javascript
     * {
     *     transport : {
     *         load : {
     *             url : 'sync.php'
     *         }
     *     }
     * }
     * ```
     *
     * When read at runtime, it will return the value from `transport.sync.url`.
     *
     * @prp {String}
     * @category Common
     */
    syncUrl: null,
    /**
     * Specify as `true` to force sync requests to be sent when calling `sync()`, even if there are no local
     * changes. Useful in a polling scenario, to keep client up to date with the backend.
     * @prp {Boolean}
     * @category CRUD
     */
    forceSync: null,
    /**
     * Set to `true` to make STM ignore changes coming from the backend. This will allow user to only undo redo
     * local changes.
     * @prp {Boolean}
     */
    ignoreRemoteChangesInSTM: false,
    /** Set to `false` to only include the id of a removed parent node in the request to the backend (also
     * affects programmatic calls to get {@link #property-changes} etc.), and not the ids of its children.
     *
     * Consider the following scenario, removing parent 1:
     *
     * ```javascript
     * {
     *   id : 1,
     *   children : [
     *     { id : 11 },
     *     { id : 12 }
     *   ]
     * }
     * ```
     *
     * With `includeChildrenInRemoveRequest : false`, the backend will only receive the removal of id 1.
     *
     * With `includeChildrenInRemoveRequest : true` (the default), the backend will receive the removal of id 1,
     * 11 and 12.
     *
     * @config {Boolean}
     * @default true
     * @category CRUD
     */
    includeChildrenInRemoveRequest: true,
    /**
     * Specifies the way to handle conflicts happening when applying `sync` responses to served stores,
     * The conflicts could happen when remote coming changes interfere with local changes made after sending
     * the request.
     *
     * Supported values are:
     *
     * - `local` local modifications are preserved and corresponding remote changes get rejected
     * - `remote` remote changes gets applied overriding the local modifications.
     *
     * @config {'local' | 'remote'}
     * @default
     * @internal
     */
    syncConflictResolution: "local"
  }), __publicField(_a5, "delayable", {
    // Postponed to next frame, to allow Scheduler created after CrudManager to inject its stores
    // (timeRanges, resourceTimeRanges)
    doAutoLoad: "raf"
  }), _a5;
};

// ../Scheduler/lib/Scheduler/crud/transport/AjaxTransport.js
var AjaxTransport_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    /**
     * Configuration of the AJAX requests used by _Crud Manager_ to communicate with a server-side.
     *
     * ```javascript
     * transport : {
     *     load : {
     *         url       : 'http://mycool-server.com/load.php',
     *         // HTTP request parameter used to pass serialized "load"-requests
     *         paramName : 'data',
     *         // pass extra HTTP request parameter
     *         params    : {
     *             foo : 'bar'
     *         }
     *     },
     *     sync : {
     *         url     : 'http://mycool-server.com/sync.php',
     *         // specify Content-Type for requests
     *         headers : {
     *             'Content-Type' : 'application/json'
     *         }
     *     }
     * }
     * ```
     *
     * Since the class uses Fetch API you can use any of the
     * [Request interface](https://developer.mozilla.org/en-US/docs/Web/API/Request) options:
     *
     * ```javascript
     * transport : {
     *     load : {
     *         url         : 'http://mycool-server.com/load.php',
     *         // HTTP request parameter used to pass serialized "load"-requests
     *         paramName   : 'data',
     *         // pass few Fetch API options
     *         method      : 'GET',
     *         credentials : 'include',
     *         cache       : 'no-cache'
     *     },
     *     sync : {
     *         url         : 'http://mycool-server.com/sync.php',
     *         // specify Content-Type for requests
     *         headers     : {
     *             'Content-Type' : 'application/json'
     *         },
     *         credentials : 'include'
     *     }
     * }
     * ```
     *
     * An object where you can set the following possible properties:
     * @config {Object} transport
     * @property {Object} [transport.load] Load requests configuration:
     * @property {String} [transport.load.url] URL to request for data loading.
     * @property {String} [transport.load.method='GET'] HTTP method to be used for load requests.
     * @property {String} [transport.load.paramName='data'] Name of the parameter that will contain a serialized `load`
     * request. The value is mandatory for requests using `GET` method (default for `load`) so if the value is not
     * provided `data` string is used as default.
     * This value is optional for HTTP methods like `POST` and `PUT`, the request body will be used for data
     * transferring in these cases.
     * @property {Object} [transport.load.params] An object containing extra HTTP parameters to pass to the server when
     * sending a `load` request.
     *
     * ```javascript
     * transport : {
     *     load : {
     *         url       : 'http://mycool-server.com/load.php',
     *         // HTTP request parameter used to pass serialized "load"-requests
     *         paramName : 'data',
     *         // pass extra HTTP request parameter
     *         // so resulting URL will look like: http://mycool-server.com/load.php?userId=123456&data=...
     *         params    : {
     *             userId : '123456'
     *         }
     *     },
     *     ...
     * }
     * ```
     * @property {Object<String,String>} [transport.load.headers] An object containing headers to pass to each server request.
     *
     * ```javascript
     * transport : {
     *     load : {
     *         url       : 'http://mycool-server.com/load.php',
     *         // HTTP request parameter used to pass serialized "load"-requests
     *         paramName : 'data',
     *         // specify Content-Type for "load" requests
     *         headers   : {
     *             'Content-Type' : 'application/json'
     *         }
     *     },
     *     ...
     * }
     * ```
     * @property {Object} [transport.sync] Sync requests (`sync` in further text) configuration:
     * @property {String} [transport.sync.url] URL to request for `sync`.
     * @property {String} [transport.sync.method='POST'] HTTP request method to be used for `sync`.
     * @property {String} [transport.sync.paramName=undefined] Name of the parameter in which `sync` data will be
     * transferred. This value is optional for requests using methods like `POST` and `PUT`, the request body will be
     * used for data transferring in this case (default for `sync`). And the value is mandatory for requests using `GET`
     * method (if the value is not provided `data` string will be used as fallback).
     * @property {Object} [transport.sync.params] HTTP parameters to pass with an HTTP request handling `sync`.
     *
     * ```javascript
     * transport : {
     *     sync : {
     *         url    : 'http://mycool-server.com/sync.php',
     *         // extra HTTP request parameter
     *         params : {
     *             userId : '123456'
     *         }
     *     },
     *     ...
     * }
     * ```
     * @property {Object<String,String>} [transport.sync.headers] HTTP headers to pass with an HTTP request handling `sync`.
     *
     * ```javascript
     * transport : {
     *     sync : {
     *         url     : 'http://mycool-server.com/sync.php',
     *         // specify Content-Type for "sync" requests
     *         headers : {
     *             'Content-Type' : 'application/json'
     *         }
     *     },
     *     ...
     * }
     * ```
     * @category CRUD
     */
    static get defaultMethod() {
      return {
        load: "GET",
        sync: "POST"
      };
    }
    /**
     * Cancels an ongoing request.
     * @param {Promise} requestPromise The Promise object wrapping the Request to be cancelled.
     * The _requestPromise_ is the value returned from the corresponding {@link #function-sendRequest} call.
     * @category CRUD
     */
    cancelRequest(requestPromise, reject) {
      var _a6;
      (_a6 = requestPromise.abort) == null ? void 0 : _a6.call(requestPromise);
      if (!this.isDestroying) {
        reject == null ? void 0 : reject({ cancelled: true });
      }
    }
    shouldUseBodyForRequestData(packCfg, method, paramName) {
      return !(method === "HEAD" || method === "GET") && !paramName;
    }
    /**
     * Sends a _Crud Manager_ request to the server.
     * @param {Object} request The request configuration object having following properties:
     * @param {'load'|'sync'} request.type The request type. Either `load` or `sync`.
     * @param {String} request.url The URL for the request. Overrides the URL defined in the `transport` object
     * @param {String} request.data The encoded _Crud Manager_ request data.
     * @param {Object} request.params An object specifying extra HTTP params to send with the request.
     * @param {Function} request.success A function to be started on successful request transferring.
     * @param {String} request.success.rawResponse `Response` object returned by the [fetch api](https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API).
     * @param {Function} request.failure A function to be started on request transfer failure.
     * @param {String} request.failure.rawResponse `Response` object returned by the [fetch api](https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API).
     * @param {Object} request.thisObj `this` reference for the above `success` and `failure` functions.
     * @returns {Promise} The fetch Promise object.
     * @fires beforeSend
     * @async
     * @category CRUD
     */
    sendRequest(request) {
      const me = this, { data } = request, transportConfig = me.transport[request.type] || {}, requestConfig = Objects.assign({}, transportConfig);
      if (request.url) {
        requestConfig.url = request.url;
      }
      requestConfig.method = requestConfig.method || _a5.defaultMethod[request.type];
      requestConfig.params = Objects.assign(requestConfig.params || {}, request.params);
      let { paramName } = requestConfig;
      if (me.shouldUseBodyForRequestData(transportConfig, requestConfig.method, paramName)) {
        requestConfig.body = data;
        requestConfig.headers = requestConfig.headers || {};
        requestConfig.headers["Content-Type"] = requestConfig.headers["Content-Type"] || "application/json";
      } else {
        paramName = paramName || "data";
        requestConfig.params[paramName] = data;
      }
      if (!requestConfig.url) {
        throw new Error("Trying to request without URL specified");
      }
      delete requestConfig.paramName;
      let ajaxPromise, resultPromise;
      function performSend() {
        requestConfig.queryParams = requestConfig.params;
        delete requestConfig.params;
        let cancelled = false;
        const fetchOptions = Objects.assign({}, requestConfig);
        ajaxPromise = AjaxHelper.fetch(requestConfig.url, fetchOptions);
        return ajaxPromise.catch((error) => {
          var _a6, _b2;
          ajaxPromise.done = true;
          (_a6 = me.trigger) == null ? void 0 : _a6.call(me, "responseReceived", { success: false });
          const signal = (_b2 = fetchOptions.abortController) == null ? void 0 : _b2.signal;
          if (signal) {
            cancelled = signal.aborted;
            if (!cancelled) {
              console.warn(error);
            }
          }
          return { error, cancelled };
        }).then((response) => {
          var _a6;
          ajaxPromise.done = true;
          (_a6 = me.trigger) == null ? void 0 : _a6.call(me, "responseReceived", { success: Boolean(response == null ? void 0 : response.ok) });
          const callback = (response == null ? void 0 : response.ok) ? request.success : request.failure;
          return callback == null ? void 0 : callback.call(request.thisObj || me, response, fetchOptions, request);
        });
      }
      const beforeSendResult = me.trigger("beforeSend", {
        params: requestConfig.params,
        requestType: request.type,
        requestConfig,
        config: request
      });
      if (Objects.isPromise(beforeSendResult)) {
        resultPromise = beforeSendResult.then(performSend);
      } else {
        resultPromise = performSend();
      }
      resultPromise.abort = () => {
        var _a6;
        if (!ajaxPromise.done) {
          (_a6 = ajaxPromise.abort) == null ? void 0 : _a6.call(ajaxPromise);
          resultPromise.aborted = true;
        }
      };
      return resultPromise;
    }
  }, __publicField(_a5, "$name", "AjaxTransport"), _a5;
};

// ../Scheduler/lib/Scheduler/crud/encoder/JsonEncoder.js
var JsonEncoder_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    static get defaultConfig() {
      return {
        /**
         * Configuration of the JSON encoder used by the _Crud Manager_.
         *
         * @config {Object}
         * @property {Object} encoder.requestData Static data to send with the data request.
         *
         * ```javascript
         * new CrudManager({
         *     // add static "foo" property to all requests data
         *     encoder : {
         *         requestData : {
         *             foo : 'Bar'
         *         }
         *     },
         *     ...
         * });
         * ```
         *
         * The above snippet will result adding "foo" property to all requests data:
         *
         * ```json
         *     {
         *         "requestId"   : 756,
         *         "type"        : "load",
         *
         *         "foo"         : "Bar",
         *
         *         "stores"      : [
         *             ...
         * ```
         * @category CRUD
         */
        encoder: {}
      };
    }
    /**
     * Encodes a request object to _JSON_ encoded string. If encoding fails (due to circular structure), it returns null.
     * Supposed to be overridden in case data provided by the _Crud Manager_ has to be transformed into format requested by server.
     * @param {Object} requestData The request to encode.
     * @returns {String} The encoded request.
     * @category CRUD
     */
    encode(requestData) {
      var _a6;
      requestData = Object.assign({}, (_a6 = this.encoder) == null ? void 0 : _a6.requestData, requestData);
      return StringHelper.safeJsonStringify(requestData);
    }
    /**
     * Decodes (parses) a _JSON_ response string to an object. If parsing fails, it returns null.
     * Supposed to be overridden in case data provided by server has to be transformed into format requested by the _Crud Manager_.
     * @param {String} responseText The response text to decode.
     * @returns {Object} The decoded response.
     * @category CRUD
     */
    decode(responseText) {
      return StringHelper.safeJsonParse(responseText);
    }
  }, __publicField(_a5, "$name", "JsonEncoder"), _a5;
};

// ../Scheduler/lib/Scheduler/data/mixin/ProjectCrudManager.js
var ProjectCrudManager_default = (Target) => class ProjectCrudManager extends (Target || Base).mixin(AbstractCrudManagerMixin_default, AjaxTransport_default, JsonEncoder_default) {
  //region Config
  static get defaultConfig() {
    return {
      project: null
    };
  }
  startConfigure(config) {
    this.getConfig("project");
    super.startConfigure(config);
    this._changesToClear = /* @__PURE__ */ new Map();
  }
  async doAutoLoad() {
    const { project } = this;
    if (project) {
      await project.commitAsync();
    }
    return super.doAutoLoad();
  }
  applyProjectResponse(response) {
    const me = this, { project } = me;
    me.applyingProjectResponse = true;
    if ("$input" in response) {
      response = response.$input;
    }
    if (Object.isFrozen(response)) {
      response = Object.assign({}, response);
    }
    const startDateField = project.fieldMap.startDate, endDateField = project.fieldMap.endDate, startDate = ObjectHelper.getPath(response, startDateField.dataSource), endDate = ObjectHelper.getPath(response, endDateField.dataSource);
    if (typeof startDate === "string") {
      ObjectHelper.setPath(response, startDateField.dataSource, startDateField.convert(startDate));
    }
    if (typeof endDate === "string") {
      ObjectHelper.setPath(response, endDateField.dataSource, endDateField.convert(endDate));
    }
    project.setByDataSource(response);
    me._changesToClear.set(me, response);
    me.applyingProjectResponse = false;
  }
  async loadCrudManagerData(response, options = {}) {
    const me = this, { project } = me;
    me.suspendChangeTracking();
    await super.loadCrudManagerData(...arguments);
    if (!me.applyingResponse && (response == null ? void 0 : response.project)) {
      if (project.delayEnteringReplica && project.hasDataInStores) {
        project.ion({
          recordsUnlinked: () => {
            me.suspendChangeTracking();
            me.applyProjectResponse(response.project);
            me.resumeChangeTracking();
          },
          once: true
        });
      } else {
        me.applyProjectResponse(response.project);
      }
    }
    me.resumeChangeTracking();
    if (!me.applyingResponse) {
      await (project == null ? void 0 : project.commitAsync());
    }
  }
  async sync() {
    const { project } = this;
    this.suspendAutoSync();
    this.suspendChangeTracking();
    if (project) {
      await project.commitAsync();
    }
    if (this.isDestroying) {
      return;
    }
    this.resumeChangeTracking(true);
    this.resumeAutoSync(false);
    return super.sync();
  }
  async applyResponse(request, response, options) {
    var _a5, _b2, _c2, _d, _e, _f;
    const me = this, { project } = me;
    if (me.isDestroyed || (project == null ? void 0 : project.isDestroyed)) {
      return;
    }
    me.applyingResponse = true;
    me.trigger("beforeApplyResponse");
    me.crudStores.forEach((storeDesc) => storeDesc.store.suspendFilterOnUpdate());
    const responseType = me.trackResponseType && response.type || request.type, projectConfig = response == null ? void 0 : response.project;
    if (projectConfig) {
      if (responseType === "load" && project.delayEnteringReplica && project.hasDataInStores) {
        project.ion({
          recordsUnlinked: () => {
            me.suspendChangeTracking();
            me.applyProjectResponse(projectConfig, request);
            me.resumeChangeTracking();
          },
          once: true
        });
      } else {
        me.applyProjectResponse(projectConfig, request);
        (_a5 = project.graph) == null ? void 0 : _a5.unScheduleAutoCommit();
      }
    }
    await super.applyResponse(request, response, options);
    if (project) {
      const responseType2 = me.trackResponseType && response.type || request.type, propagationFlag = `propagating${StringHelper.capitalize(responseType2)}Changes`;
      me.suspendAutoSync();
      if (responseType2 === "sync") {
        const projectChangesToClear = me.getRecordChangesToClear(
          {
            store: {
              modelClass: project.constructor
            }
          },
          ((_b2 = request == null ? void 0 : request.pack) == null ? void 0 : _b2.project) || {},
          projectConfig,
          project.modificationDataToWrite,
          project
        );
        if (projectChangesToClear) {
          me._changesToClear.set(project, projectChangesToClear);
        }
      }
      me[propagationFlag] = true;
      const loud = project.isInitialCommit && !project.silenceInitialCommit;
      await project.commitAsync();
      me[propagationFlag] = false;
      (_c2 = me.resumeAutoSync) == null ? void 0 : _c2.call(me, loud);
      (_d = me.commitRespondedChanges) == null ? void 0 : _d.call(me);
    }
    (_e = me.crudStores) == null ? void 0 : _e.forEach((storeDesc) => storeDesc.store.resumeFilterOnUpdate());
    (_f = me.trigger) == null ? void 0 : _f.call(me, "applyResponse", { request, response, options });
    me.applyingResponse = false;
  }
  applySyncResponse(...args) {
    var _a5;
    const me = this, stmDisabled = (_a5 = me.project) == null ? void 0 : _a5.stm.disabled;
    if (stmDisabled === false && me.shouldIgnoreRemoteChangesInSTM) {
      me.project.stm.disable();
    }
    me.suspendAutoSync();
    super.applySyncResponse(...args);
    me.resumeAutoSync(false);
    if (stmDisabled === false) {
      me.project.stm.enable();
    }
  }
  shouldClearRecordFieldChange(record, field, value2) {
    const oldValue = record.getValue(field);
    field = record.getFieldDefinition(field);
    return (field == null ? void 0 : field.isEqual) ? field.isEqual(oldValue, value2) : ObjectHelper.isEqual(oldValue, value2);
  }
  commitRespondedChanges() {
    this._changesToClear.forEach((changes, record) => {
      Object.entries(changes).forEach(([key, value2]) => {
        if (!(record === this && this.project !== this) && this.shouldClearRecordFieldChange(record, key, value2)) {
          delete record.meta.modified[key];
        }
      });
    });
    this._changesToClear.clear();
  }
  applyChangesToStore(storeDesc, storeResponse, storeRequest, ...rest) {
    const changesMap = super.applyChangesToStore(storeDesc, storeResponse, storeRequest, ...rest);
    if ((changesMap == null ? void 0 : changesMap.size) && this.project) {
      for (const [id2, changes] of changesMap) {
        const record = storeDesc.store.getById(id2);
        record && this._changesToClear.set(record, changes);
      }
    }
    return changesMap;
  }
};

// ../Scheduler/lib/Scheduler/crud/mixin/LazyLoadCrudManager.js
var lazyDateStores = ["events", "resourceTimeRanges", "assignments", "timeRanges"];
var LazyLoadCrudManager_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    updateRemotePaging(remotePaging, old) {
      if (remotePaging) {
        this.lazyLoad = true;
      } else if (old) {
        this.lazyLoad = false;
      }
    }
    updatePageSize() {
      if (!this.isConfiguring) {
        this.load();
      }
    }
    updateLazyLoad(lazyLoad, old) {
      if (!lazyLoad && !old) {
        return;
      }
      const me = this, { internalLoad, crudStores } = me;
      crudStores.forEach(({ store }) => me.storeShouldLazyLoad(store) && me.setupLazyStore(store));
      if (lazyLoad) {
        if (!internalLoad) {
          me.internalLoad = me.load;
          me.load = me.internalLazyLoad;
        }
      } else if (internalLoad) {
        me.load = internalLoad;
      }
    }
    changeRequestData(requestData, old) {
      if (requestData === "defaultRequestData") {
        return this.defaultRequestData;
      }
      return requestData;
    }
    afterAddStore(stores2) {
      stores2.forEach(({ store }) => this.storeShouldLazyLoad(store) && this.setupLazyStore(store));
    }
    setupPagingStore(store) {
      if (this.remotePaging) {
        const { isResourceStore } = store;
        store.remotePaging = true;
        store.syncDataOnLoad = false;
        if (isResourceStore) {
          store._pageSize = this.pageSize;
        } else {
          store.lazyLoad = true;
          store.lazyLoad.$isCrudManager = true;
        }
        if (isResourceStore || store.isEventStore) {
          store.requestData = (params) => this.doLazyLoad(params, isResourceStore);
        } else {
          store.requestData = null;
        }
      } else {
        store.remotePaging = false;
      }
    }
    setupLazyStore(store) {
      var _a6;
      if (this.remotePaging) {
        return this.setupPagingStore(store);
      }
      let { lazyLoad } = this;
      if (lazyLoad) {
        const { isResourceStore } = store;
        if (lazyLoad !== true) {
          const {
            chunkSize,
            bufferUnit,
            bufferAmount
          } = lazyLoad;
          lazyLoad = {};
          if (isResourceStore && chunkSize) {
            lazyLoad.chunkSize = chunkSize;
          } else if (!isResourceStore) {
            if (bufferUnit) {
              lazyLoad.bufferUnit = bufferUnit;
            }
            if (bufferAmount) {
              lazyLoad.bufferAmount = bufferAmount;
            }
          }
        }
        store.lazyLoad = lazyLoad;
        store.lazyLoad.$isCrudManager = true;
        store.syncDataOnLoad = false;
        if (isResourceStore || store.isEventStore) {
          store.requestData = (params) => this.doLazyLoad(params, isResourceStore);
        } else {
          store.requestData = null;
        }
      } else if ((_a6 = store.lazyLoad) == null ? void 0 : _a6.$isCrudManager) {
        store.lazyLoad = false;
      }
    }
    internalLazyLoad(options) {
      var _a6;
      const me = this;
      me.crudLoaded = false;
      options && ((_a6 = me._loadOptions) != null ? _a6 : me._loadOptions = []).push(options);
      return me.resourceStore.load();
    }
    loadCrudStore(store, data, options) {
      if (this.lazyLoad && this.storeShouldLazyLoad(store)) {
        return;
      }
      return super.loadCrudStore(store, data, options);
    }
    getLazyDateStores(skipEventStore) {
      return this.crudStores.filter((store) => {
        var _a6;
        return lazyDateStores.includes(store.storeId) && (!skipEventStore || !store.store.isEventStore) && (!store.store.isAssignmentStore || !((_a6 = this.eventStore) == null ? void 0 : _a6.usesSingleAssignment));
      });
    }
    storeShouldLazyLoad(store) {
      return store.isResourceStore || this.getLazyDateStores().some((s) => s.store === store);
    }
    // Currently, this function will be called from the resourceStore requesting and index or from the eventStore
    // requesting a date range.
    async doLazyLoad({ isReload, ...params }, isResourceStore) {
      if (!params.startDate || !params.endDate) {
        return;
      }
      const me = this, dateStores = me.getLazyDateStores(!isResourceStore), isResourceTree = isResourceStore && me.resourceStore.isTree, pending = me._lazyRequestPromises || (me._lazyRequestPromises = []), stores2 = me.crudStores.filter((s) => !me.crudLoaded || (lazyDateStores.includes(s.storeId) || isResourceStore && s.store.isResourceStore)).map((s) => s.storeId), promise = Promise.all(pending).then(() => me.requestData({ ...params, stores: stores2 })), queueParams = { ...params, promise };
      if (isResourceTree) {
        queueParams.startIndex = -1;
        queueParams.count = 1;
      }
      dateStores.forEach(({ store }) => {
        if (isResourceStore && (isReload || me.remotePaging)) {
          me.remotePaging && store.clear(true);
          store.lazyLoad.clear();
        }
        store.lazyLoad.triggerLazyLoadStart();
        store.lazyLoad.addToQueue(queueParams);
      });
      pending.push(promise);
      const response = await promise, { rows, total } = isResourceStore ? response.resources : response.events;
      if (isResourceTree) {
        const { idField } = me.resourceStore.modelClass;
        queueParams.resourceIds = rows.map((r) => r[idField]);
      }
      queueParams.promise = void 0;
      dateStores.forEach(({ store: { lazyLoad }, storeId }) => {
        if (isResourceTree) {
          delete lazyLoad.loadQueue[-1];
          lazyLoad.addToQueue(queueParams);
        }
        if (response[storeId]) {
          lazyLoad.addData(response[storeId].rows, queueParams, isReload);
        }
        lazyLoad.triggerLazyLoadEnd();
      });
      pending.splice(pending.indexOf(promise), 1);
      return {
        data: rows,
        total: isResourceStore ? total : void 0
      };
    }
    async defaultRequestData({ stores: stores2, ...params }) {
      var _a6, _b2;
      return await this.internalLoad(ObjectHelper.merge((_b2 = (_a6 = this._loadOptions) == null ? void 0 : _a6.shift()) != null ? _b2 : {}, {
        type: "load",
        params,
        stores: stores2
      })).then((r) => r.response);
    }
  }, __publicField(_a5, "$name", "LazyLoadCrudManager"), __publicField(_a5, "configurable", {
    /**
     * If set to `true`, or a config object, this makes the CrudManager load records only when needed. When a record
     * or a date range that is not already loaded is requested, a load request will be made to the specified URL.
     * More more details about lazy loading, see the [guide](#Grid/guides/data/lazyloading.md)
     * @param {Object} lazyLoad Lazy load config
     * @param {Number} lazyLoad.chunkSize The number of records to be loaded before and after the requested
     * index (only affects the ResourceStore).
     * @param {DurationUnit} bufferUnit Used together with bufferAmount to calculate the start and end dates of each
     * load request. The value is added to the current visible start or end date. Defaults to the visible time span
     * length (does not affect ResourceStore).
     * @param {Number} bufferAmount See `bufferUnit`
     * @prp {Boolean|Object}
     */
    lazyLoad: false,
    /**
     * If set to `true`, this makes the CrudManager load pages of data, instead of loading the complete dataset at
     * once. The requests made to the {@link Scheduler.crud.AbstractCrudManagerMixin#config-loadUrl} will contain
     * params with info about the current dataset being requested:
     * * `page` - The resource page
     * * `pageSize` - The resource page size
     * * `startDate` - The start date of the calculated timespan
     * * `endDate` - The end date of the calculated timespan
     *
     * For more details about paging, see the [guide](#Grid/guides/data/paging.md)
     * @config {Boolean}
     */
    remotePaging: false,
    /**
     * The number of Resource records each page should contain, when using {@link #config-remotePaging}
     * @config {Number}
     */
    pageSize: 50,
    /**
     * When the CrudManager/Project is configured with {@link #config-lazyLoad} set to `true`, you can configure
     * your own data fetching logic by implementing this function. Doing this will override the built-in fetching
     * mechanism using the {@link Scheduler/data/CrudManager#config-loadUrl}.
     *
     * When implementing this, it is expected that what is returned is an object fulfilling the
     * {@link Scheduler/data/CrudManager contract} of a regular CrudManager/Project load call.
     *
     * @config {Function}
     * @param {LazyLoadCrudManagerRequestParams} params Object containing info of what data is requested
     * @returns {CrudManagerRequestResponse}
     * @async
     */
    requestData: "defaultRequestData"
  }), _a5;
};

// ../Scheduler/lib/Scheduler/crud/AbstractCrudManager.js
var AbstractCrudManager = class extends Base.mixin(AbstractCrudManagerMixin_default) {
  //region Default config
  /**
   * The server revision stamp.
   * The _revision stamp_ is a number which should be incremented after each server-side change.
   * This property reflects the current version of the data retrieved from the server and gets updated after each
   * {@link Scheduler/crud/AbstractCrudManagerMixin#function-load} and {@link Scheduler/crud/AbstractCrudManagerMixin#function-sync} call.
   * @property {Number}
   * @readonly
   */
  get revision() {
    return this.crudRevision;
  }
  set revision(value2) {
    this.crudRevision = value2;
  }
  /**
   * Get or set data of {@link #property-crudStores} as a JSON string.
   *
   * Get a JSON string:
   * ```javascript
   *
   * const jsonString = scheduler.crudManager.json;
   *
   * // returned jsonString:
   * '{"eventsData":[...],"resourcesData":[...],...}'
   *
   * // object representation of the returned jsonString:
   * {
   *     resourcesData    : [...],
   *     eventsData       : [...],
   *     assignmentsData  : [...],
   *     dependenciesData : [...],
   *     timeRangesData   : [...],
   *     // data from other stores
   * }
   * ```
   *
   * Set a JSON string (to populate the CrudManager stores):
   *
   * ```javascript
   * scheduler.crudManager.json = '{"eventsData":[...],"resourcesData":[...],...}'
   * ```
   *
   * @property {String}
   */
  get json() {
    const data = this.crudStoresJSON;
    return StringHelper.safeJsonStringify(data);
  }
  set json(json) {
    if (typeof json === "string") {
      json = StringHelper.safeJsonParse(json);
    }
    this.crudStoresJSON = json;
  }
  static get defaultConfig() {
    return {
      /**
       * Sets the list of stores controlled by the CRUD manager.
       *
       * When adding a store to the CrudManager, make sure the server response format is correct for `load` and `sync` requests.
       * Learn more in the [Working with data](#Scheduler/guides/data/crud_manager_in_depth.md#loading-data) guide.
       *
       * Store can be provided as in instance, using its `id` or as an {@link #typedef-CrudManagerStoreDescriptor}
       * object.
       * @config {Core.data.Store[]|String[]|CrudManagerStoreDescriptor[]|StoreConfig[]}
       */
      stores: null
      /**
       * Encodes request to the server.
       * @function encode
       * @param {Object} request The request to encode.
       * @returns {String} The encoded request.
       * @abstract
       */
      /**
       * Decodes response from the server.
       * @function decode
       * @param {String} response The response to decode.
       * @returns {Object} The decoded response.
       * @abstract
       */
    };
  }
  //endregion
  //region Init
  construct(config = {}) {
    if (config.stores) {
      config.crudStores = config.stores;
      delete config.stores;
    }
    super.construct(config);
  }
  //endregion
  //region inline data
  /**
   * Returns the data from all CrudManager `crudStores` in a format that can be consumed by `inlineData`.
   *
   * Used by JSON.stringify to correctly convert this CrudManager to json.
   *
   * The returned data is identical to what {@link Scheduler/crud/AbstractCrudManager#property-inlineData} contains.
   *
   * ```javascript
   *
   * const json = scheduler.crudManager.toJSON();
   *
   * // json:
   * {
   *     eventsData : [...],
   *     resourcesData : [...],
   *     dependenciesData : [...],
   *     assignmentsData : [...],
   *     timeRangesData : [...],
   *     resourceTimeRangesData : [...],
   *     // ... other stores data
   * }
   * ```
   *
   * Output can be consumed by `inlineData`.
   *
   * ```javascript
   * const json = scheduler.crudManager.toJSON();
   *
   * // Plug it back in later
   * scheduler.crudManager.inlineData = json;
   * ```
   *
   * @function toJSON
   * @returns {Object}
   * @category JSON
   */
  toJSON() {
    return this.crudStoresJSON;
  }
  /**
   * Get or set data of CrudManager stores. The returned data is identical to what
   * {@link Scheduler/crud/AbstractCrudManager#function-toJSON} returns:
   *
   * ```javascript
   *
   * const data = scheduler.crudManager.inlineData;
   *
   * // data:
   * {
   *     eventsData : [...],
   *     resourcesData : [...],
   *     dependenciesData : [...],
   *     assignmentsData : [...],
   *     timeRangesData : [...],
   *     resourceTimeRangesData : [...],
   *     ... other stores data
   * }
   *
   *
   * // Plug it back in later
   * scheduler.crudManager.inlineData = data;
   * ```
   *
   * @property {Object}
   */
  get inlineData() {
    return this.crudStoresJSON;
  }
  set inlineData(data) {
    this.crudStoresJSON = data;
  }
  //endregion
  //region Store collection (add, remove, get & iterate)
  set stores(stores2) {
    if (stores2 !== this.crudStores) {
      this.crudStores = stores2;
    }
  }
  /**
   * A list of registered stores whose server communication will be collected into a single batch.
   * Each store is represented by a _store descriptor_.
   * @member {CrudManagerStoreDescriptor[]} stores
   */
  get stores() {
    return this.crudStores;
  }
  //endregion
  /**
   * Returns true if the crud manager is currently loading data
   * @property {Boolean}
   * @readonly
   * @category CRUD
   */
  get isLoading() {
    return this.isCrudManagerLoading;
  }
  /**
   * Adds a store to the collection.
   *
   * ```javascript
   * // append stores to the end of collection
   * crudManager.addStore([
   *     store1,
   *     // store id
   *     'bar',
   *     // store descriptor
   *     {
   *         storeId : 'foo',
   *         store   : store3
   *     },
   *     {
   *         storeId         : 'bar',
   *         store           : store4,
   *         // to write all fields of modified records
   *         writeAllFields  : true
   *     }
   * ]);
   * ```
   *
   * **Note:** Order in which stores are kept in the collection is very essential sometimes.
   * Exactly in this order the loaded data will be put into each store.
   *
   * When adding a store to the CrudManager, make sure the server response format is correct for `load` and `sync`
   * requests. Learn more in the [Working with data](#Scheduler/guides/data/crud_manager_in_depth.md#loading-data) guide.
   *
   * @param {Core.data.Store|String|CrudManagerStoreDescriptor|Core.data.Store[]|String[]|CrudManagerStoreDescriptor[]} store
   * A store or list of stores. Each store might be specified by its instance, `storeId` or _descriptor_.
   * @param {Number} [position] The relative position of the store. If `fromStore` is specified the position will be
   * taken relative to it.
   * If not specified then store(s) will be appended to the end of collection.
   * Otherwise, it will be an index in stores collection.
   *
   * ```javascript
   * // insert stores store4, store5 to the start of collection
   * crudManager.addStore([ store4, store5 ], 0);
   * ```
   *
   * @param {String|Core.data.Store|CrudManagerStoreDescriptor} [fromStore] The store relative to which position
   * should be calculated. Can be defined as a store identifier, instance or descriptor (the result of
   * {@link Scheduler/crud/AbstractCrudManagerMixin#function-getStoreDescriptor} call).
   *
   * ```javascript
   * // insert store6 just before a store having storeId equal to 'foo'
   * crudManager.addStore(store6, 0, 'foo');
   *
   * // insert store7 just after store3 store
   * crudManager.addStore(store7, 1, store3);
   * ```
   */
  addStore(...args) {
    return this.addCrudStore(...args);
  }
  removeStore(...args) {
    return this.removeCrudStore(...args);
  }
  getStore(...args) {
    return this.getCrudStore(...args);
  }
  hasChanges(...args) {
    return this.crudStoreHasChanges(...args);
  }
  loadData(...args) {
    return this.loadCrudManagerData(...args);
  }
};
AbstractCrudManager._$name = "AbstractCrudManager";

// ../Scheduler/lib/Scheduler/model/mixin/ProjectModelCommon.js
var ProjectModelCommon_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Model) {
    static get configurable() {
      return {
        // Documented in Gantt/Scheduler/SchedulerPro version of ./model/ProjectModel since types differ
        assignments: null,
        dependencies: null,
        resources: null,
        timeRanges: null,
        /**
         * Project data as a JSON string, used to populate its stores.
         *
         * ```javascript
         * const project = new ProjectModel({
         *     json : '{"eventsData":[...],"resourcesData":[...],...}'
         * }
         * ```
         *
         * @config {String}
         * @category Inline data
         */
        json: null,
        /**
         * Experimental hook that lets the app determine if a bound dataset needs syncing with the store or not, and
         * if it does - which records that should be processed. Only called for stores that are configured with
         * `syncDataOnLoad: true` (which is the default in the React, Angular and Vue wrappers).
         *
         * {@note}This is an experimental API that might change in any release until it has been finalized.{/@note}
         *
         * The main use case for this is for frameworks that update all state on any state change (like React), to
         * not have to reapply the full bound dataset each time (which becomes costly).
         *
         * If the app has a cheap way of determining (for example using a timestamp) that the dataset has no
         * changes, return `false` from this hook to prevent the store from syncing at all at that time.
         *
         * ```javascript
         * shouldSyncDataOnLoad({ store, data }) {
         *   if (store === myEventStore && timestamp <= lastUpdateTimestamp) {
         *     // Prevent sync when the event store has not changed since last time
         *     // (pseudocode)
         *     return false;
         *   }
         * }
         * ```
         *
         * If the app knows that something has changed, return a `Set` with ids for the records that should be
         * further processed by the sync logic.
         *
         * ```javascript
         * shouldSyncDataOnLoad({ store, data }) {
         *   if (store === myEventStore && timestamp > lastUpdateTimestamp) {
         *     // Sync only these records (pseudocode)
         *     return new Set([6, 110, 586]);
         *   }
         * }
         * ```
         *
         * @prp {Function}
         * @param {Object} options Options passed by the store to this hook
         * @param {Core.data.Store} options.store Store about to be synced
         * @param {Core.data.Model} options.records Records currently in the store
         * @param {Object[]} options.data Incoming data
         * @returns {Set|Boolean} Return `false` to prevent the store from syncing, or a set of
         * record ids that need further processing (for records that has some kind of change, eg. an update, removal
         * or addition)
         * @advanced
         * @category Advanced
         */
        shouldSyncDataOnLoad: null,
        /**
         * Specifies the output format of {@link #function-toJSON}.
         *
         * - `inlineData` - Return all crud store data, plus this project fields (under the `project` key). For example:
         * ```json
         * {
         *     "project" : {
         *         "startDate"    : "2024-03-22",
         *         "endDate"      : "2024-04-22",
         *         "hoursPerDay"  : 8,
         *         "daysPerWeek"  : 5,
         *         "daysPerMonth" : 20
         *     },
         *     "eventsData"             : [...],
         *     "resourcesData"          : [...],
         *     "assignmentsData"        : [...],
         *     "dependenciesData"       : [...],
         *     "resourceTimeRangesData" : [...],
         *     "timeRangesData"         : [...]
         * }
         * ```
         * - `model` - Return only the project fields. For example:
         * ```json
         * {
         *     "startDate"    : "2024-03-22",
         *     "endDate"      : "2024-04-22",
         *     "hoursPerDay"  : 8,
         *     "daysPerWeek"  : 5,
         *     "daysPerMonth" : 20
         * }
         * ```
         *
         * @config {'inlineData'|'model'}
         * @category Inline data
         */
        toJSONResultFormat: "inlineData",
        /**
         * By default, the stores of a project use the raw data objects passed to them as the data source for their
         * records if data is loaded remotely (using an `AjaxStore` or a `CrudManager`). For data supplied inline,
         * the data objects are instead by default cloned to avoid the original data object being modified by the
         * store.
         *
         * See {@link Core.data.Store#config-useRawData} for more information on the mechanics of this.
         *
         * This can be explicitly controlled per store by setting the `useRawData` config on the store:
         *
         * ```javascript
         * // Opt in for a single store
         * const project = new ProjectModel({
         *     eventStore : {
         *       useRawData : true
         *     },
         *     ...
         * });
         * ```
         *
         * Or you can control it for all the default stores in the project by setting this config on the project:
         *
         * ```javascript
         * // Opt in for all stores in the project
         * const project = new ProjectModel({
         *    useRawData : true,
         *    ...
         * });
         * ```
         *
         * @config {Boolean}
         */
        useRawData: null
      };
    }
    // Project is a Model which triggers events, therefore it can define event handlers using `onEvent` syntax. Event
    // handler can be a string (for another instance property), or a function. Therefore, it is impossible to tell them
    // apart and project model can not expose fields.
    // https://github.com/bryntum/support/issues/7457
    static get autoExposeFields() {
      return false;
    }
    attachStore(store) {
      super.attachStore(store);
      if (this.useRawData != null) {
        store.useRawData = this.useRawData;
      }
    }
    //region Inline data
    /**
     * Returns the data from the records of the projects stores, in a format that can be consumed by
     * `loadInlineData()`.
     *
     * Used by JSON.stringify to correctly convert this record to json.
     *
     * The output format is determined by the value of
     * {@link #config-toJSONResultFormat}, as follows:
     *
     * - `inlineData` - Returns all crud store data, plus the project fields (under the `project` key)
     * - `model` - Returns only the project fields
     *
     * ```javascript
     * const project = new ProjectModel({
     *     // Include both store data and project model fields in toJSON() result
     *     toJSONResultFormat     : 'inlineData',
     *     eventsData             : [...],
     *     resourcesData          : [...],
     *     assignmentsData        : [...],
     *     dependenciesData       : [...],
     *     resourceTimeRangesData : [...],
     *     timeRangesData         : [...]
     * });
     *
     * const json = project.toJSON();
     *
     * // json:
     * {
     *     eventsData             : [...],
     *     resourcesData          : [...],
     *     assignmentsData        : [...],
     *     dependenciesData       : [...],
     *     ...
     *     project                : {
     *         addConstraintOnDateSet : false,
     *         daysPerMonth           : 13
     *         daysPerWeek            : 3
     *         ...
     *     },
     *     ...
     * }
     * ```
     *
     * Output can be consumed by `loadInlineData()`:
     *
     * ```javascript
     * const json = project.toJSON();
     *
     * // Plug it back in later
     * project.loadInlineData(json);
     * ```
     *
     * @returns {Object}
     * @category Inline data
     */
    toJSON() {
      switch (this.toJSONResultFormat) {
        case "inlineData":
          return this.inlineData;
        case "model":
          return super.toJSON();
      }
    }
    /**
     * Get or set data of project stores. The returned data is identical to what
     * {@link #function-toJSON} returns:
     *
     * ```javascript
     *
     * const data = scheduler.project.inlineData;
     *
     * // data:
     * {
     *     project                : { ... },
     *     eventsData             : [...],
     *     resourcesData          : [...],
     *     dependenciesData       : [...],
     *     assignmentsData        : [...],
     *     resourceTimeRangesData : [...],
     *     timeRangesData         : [...]
     * }
     *
     *
     * // Plug it back in later
     * scheduler.project.inlineData = data;
     * ```
     *
     * @property {Object}
     * @category Inline data
     */
    get inlineData() {
      const me = this;
      let result;
      if (me.isAbstractCrudManagerMixin) {
        result = me.crudStoresJSON;
      } else {
        result = {
          eventsData: me.eventStore.toJSON(),
          resourcesData: me.resourceStore.toJSON(),
          dependenciesData: me.dependencyStore.toJSON(),
          timeRangesData: me.timeRangeStore.toJSON(),
          resourceTimeRangesData: me.resourceTimeRangeStore.toJSON(),
          assignmentsData: me.assignmentStore.toJSON()
        };
      }
      if (me.eventStore.usesSingleAssignment && result.assignmentsData) {
        delete result.assignmentsData;
      }
      result.project = super.toJSON();
      ObjectHelper.deletePath(result, "project.id");
      ObjectHelper.deletePath(result, "project.children");
      return result;
    }
    set inlineData(inlineData) {
      this.loadInlineData(inlineData);
    }
    /**
     * Get or set project data (records from its stores) as a JSON string.
     *
     * Get a JSON string:
     *
     * ```javascript
     * const project = new ProjectModel({
     *     eventsData       : [...],
     *     resourcesData    : [...],
     *     assignmentsData  : [...],
     *     dependenciesData : [...]
     * });
     *
     * const jsonString = project.json;
     *
     * // jsonString:
     * '{"eventsData":[...],"resourcesData":[...],...}'
     * ```
     *
     * Set a JSON string (to populate the project stores):
     *
     * ```javascript
     * project.json = '{"eventsData":[...],"resourcesData":[...],...}'
     * ```
     *
     * @property {String}
     * @category Inline data
     */
    get json() {
      const { toJSONResultFormat } = this;
      this.toJSONResultFormat = "inlineData";
      const result = super.json;
      this.toJSONResultFormat = toJSONResultFormat;
      return result;
    }
    changeJson(json) {
      if (typeof json === "string") {
        json = StringHelper.safeJsonParse(json);
      }
      return json;
    }
    updateJson(json) {
      json && this.loadInlineData(json);
    }
    get assignments() {
      return this.assignmentStore.allRecords;
    }
    updateAssignments(assignments) {
      this.assignmentStore.data = assignments;
    }
    get dependencies() {
      return this.dependencyStore.allRecords;
    }
    updateDependencies(dependencies) {
      this.dependencyStore.data = dependencies;
    }
    get resources() {
      return this.resourceStore.allRecords;
    }
    updateResources(resources) {
      this.resourceStore.data = resources;
    }
    get timeRanges() {
      return this.timeRangeStore.allRecords;
    }
    getTimeRanges(startDate, endDate) {
      const store = this.timeRangeStore, ret = [];
      for (const timeSpan of store) {
        if (timeSpan.isRecurring) {
          ret.push(...timeSpan.getOccurrencesForDateRange(startDate, endDate));
        } else if (timeSpan.startDate < endDate && startDate < timeSpan.endDate) {
          ret.push(timeSpan);
        }
      }
      return ret;
    }
    updateTimeRanges(timeRanges) {
      this.timeRangeStore.data = timeRanges;
    }
    getResourceTimeRanges(startDate, endDate) {
      const store = this.resourceTimeRangeStore, ret = [];
      for (const timeSpan of store) {
        if (timeSpan.isRecurring) {
          ret.push(...timeSpan.getOccurrencesForDateRange(startDate, endDate));
        } else if (timeSpan.startDate < endDate && (!timeSpan.endDate || startDate < timeSpan.endDate)) {
          ret.push(timeSpan);
        }
      }
      return ret;
    }
    //endregion
  }, __publicField(_a5, "$name", "ProjectModelCommon"), _a5;
};

// ../Scheduler/lib/Scheduler/model/ResourceTimeRangeModel.js
var ResourceTimeRangeModel = class extends TimeSpan.mixin(RecurringTimeSpan_default) {
  get domId() {
    return `${this.constructor.domIdPrefix}-${this.id}`;
  }
  //endregion
  // Used internally to differentiate between Event and ResourceTimeRange
  get isResourceTimeRange() {
    return true;
  }
  // To match EventModel API
  get resources() {
    return this.resource ? [this.resource] : [];
  }
  // To match EventModel API
  get $linkedResources() {
    return this.resources;
  }
};
__publicField(ResourceTimeRangeModel, "$name", "ResourceTimeRangeModel");
//region Fields
__publicField(ResourceTimeRangeModel, "fields", [
  /**
   * Id of the resource this time range is associated with
   * @field {String|Number} resourceId
   */
  "resourceId",
  /**
   * Controls this time range's primary color, defaults to using current themes default time range color.
   * @field {String} timeRangeColor
   */
  "timeRangeColor"
]);
__publicField(ResourceTimeRangeModel, "relations", {
  /**
   * The associated resource, retrieved using a relation to a ResourceStore determined by the value assigned
   * to `resourceId`. The relation also lets you access all time ranges on a resource through
   * {@link Scheduler.model.ResourceModel#property-timeRanges}`.
   * @member {Scheduler.model.ResourceModel} resource
   */
  resource: {
    foreignKey: "resourceId",
    foreignStore: "resourceStore",
    relatedCollectionName: "timeRanges",
    nullFieldOnRemove: true
  }
});
__publicField(ResourceTimeRangeModel, "domIdPrefix", "resourcetimerange");
ResourceTimeRangeModel._$name = "ResourceTimeRangeModel";

// ../Scheduler/lib/Scheduler/data/ResourceTimeRangeStore.js
var ResourceTimeRangeStore = class extends AjaxStore.mixin(RecurringTimeSpansMixin_default) {
  static get defaultConfig() {
    return {
      /**
       * CrudManager must load stores in the correct order. Lowest first.
       * @private
       */
      loadPriority: 500,
      /**
       * CrudManager must sync stores in the correct order. Lowest first.
       * @private
       */
      syncPriority: 500,
      /**
       * This store should be linked to a ResourceStore to link the time ranges to resources
       * @config {Scheduler.data.ResourceStore}
       */
      resourceStore: null,
      modelClass: ResourceTimeRangeModel,
      id: "resourceTimeRanges",
      project: null
    };
  }
  get $lazyLoadPluginClass() {
    return this.isPaged ? PagedDateStoreLazyLoadPlugin : DateStoreLazyLoadPlugin;
  }
  // Link to the resource store when joining a project, to make relations work
  set project(project) {
    var _a5;
    super.project = project;
    if (!this.resourceStore && ((_a5 = project == null ? void 0 : project.resourceStore) == null ? void 0 : _a5.isStore)) {
      this.resourceStore = project.resourceStore;
    }
  }
  get project() {
    return super.project;
  }
  set resourceStore(store) {
    this._resourceStore = store;
    if (!this.isConfiguring) {
      this.initRelations(true);
    }
  }
  get resourceStore() {
    return this._resourceStore;
  }
  // Matching signature in EventStore to allow reusage of SchedulerStores#onInternalEventStoreChange()
  getResourcesForEvent(resourceTimeRange) {
    return [resourceTimeRange.resource];
  }
  /**
   * Get resource time ranges intersecting the specified date range for a resource.
   *
   * The result is sorted by `startDate`.
   *
   * @param {Object} options Options
   * @param {Scheduler.model.ResourceModel} options.resourceRecord Resource record
   * @param {Date} options.startDate Start date of the range
   * @param {Date} options.endDate End date of the range
   * @returns {Scheduler.model.ResourceTimeRangeModel[]}
   */
  getRanges({ resourceRecord, startDate, endDate }) {
    const rangesInDateRange = resourceRecord.timeRanges.flatMap((range) => {
      if (range.isRecurring) {
        return range.getOccurrencesForDateRange(startDate, endDate);
      }
      if (range.intersectsRange(startDate, endDate)) {
        return range;
      }
      return [];
    });
    return rangesInDateRange.sort((span1, span2) => span1.startDate - span2.startDate);
  }
};
__publicField(ResourceTimeRangeStore, "$name", "ResourceTimeRangeStore");
ResourceTimeRangeStore._$name = "ResourceTimeRangeStore";

// ../Scheduler/lib/Scheduler/data/plugin/StoreTimeZonePlugin.js
var StoreTimeZonePlugin = class extends InstancePlugin {
  static get pluginConfig() {
    return {
      before: ["processRecord"],
      assign: ["beforeSyncRecord", "afterSyncRecord"],
      override: ["getOccurrencesForTimeSpan"]
    };
  }
  get timeZone() {
    return this.client.project.timeZone;
  }
  // Overrides a Store's processRecord function to be able to convert records added by a dataset
  // before they are processed by the engine
  processRecord(record, isDataSet) {
    if (isDataSet || this.client.isLoadingData || record.timeZone !== void 0) {
      record.convertToTimeZone(this.timeZone);
    }
  }
  beforeSyncRecord({ record, data }) {
    if (record.timeZone != null) {
      let ignoreFields;
      if (!record.manuallyScheduled && record.constraintDate && !data.constraintDate) {
        ignoreFields = ["constraintDate"];
      }
      record.$restoreTimeZone = record.timeZone;
      record.convertToTimeZone(null, ignoreFields);
    }
  }
  afterSyncRecord({ record, data }) {
    if (record.$restoreTimeZone) {
      let ignoreFields;
      if (!record.manuallyScheduled && record.constraintDate && !data.constraintDate) {
        ignoreFields = ["constraintDate"];
      }
      record.convertToTimeZone(record.$restoreTimeZone, ignoreFields);
      record.$restoreTimeZone = null;
    }
  }
  getOccurrencesForTimeSpan(timeSpan, start, end) {
    let result = [];
    if (timeSpan.isRecurring) {
      const { timeZone } = timeSpan.recurrence, timeSpanTimeZone = timeSpan.timeZone;
      let originalStartDate;
      if (timeZone != null && timeSpanTimeZone != timeZone && (!timeSpan.project || timeSpanTimeZone == timeSpan.project.timeZone)) {
        originalStartDate = timeSpan.startDate;
        const tempStartDate = TimeZoneHelper.toTimeZone(timeSpan.getLocalDate("startDate"), timeZone);
        if (timeSpan.isResourceTimeRange) {
          timeSpan.setData("startDate", tempStartDate);
        } else {
          timeSpan._startDate = tempStartDate;
        }
      }
      result = this.overridden.getOccurrencesForTimeSpan(timeSpan, start, end);
      if (originalStartDate != null) {
        if (timeSpan.isResourceTimeRange) {
          timeSpan.setData("startDate", originalStartDate);
        } else {
          timeSpan._startDate = originalStartDate;
        }
        result.forEach((occurrence) => {
          if (occurrence !== timeSpan) {
            let startDate = TimeZoneHelper.fromTimeZone(occurrence.startDate, timeZone), endDate = TimeZoneHelper.fromTimeZone(occurrence.endDate, timeZone);
            if (timeSpanTimeZone != null) {
              startDate = TimeZoneHelper.toTimeZone(startDate, timeSpanTimeZone);
              endDate = TimeZoneHelper.toTimeZone(endDate, timeSpanTimeZone);
            }
            occurrence.setData({
              startDate,
              endDate
            });
          }
        });
      }
    }
    return result;
  }
};
__publicField(StoreTimeZonePlugin, "$name", "storeTimeZonePlugin");
StoreTimeZonePlugin._$name = "StoreTimeZonePlugin";

// ../Scheduler/lib/Scheduler/model/mixin/ProjectModelTimeZoneMixin.js
var ProjectModelTimeZoneMixin_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Model) {
    get _storesWithDates() {
      return [this.taskStore, this.timeRangeStore, this.resourceTimeRangeStore].filter((s) => s);
    }
    plugStore(store) {
      var _a6;
      if (((_a6 = store.hasPlugin) == null ? void 0 : _a6.call(store, StoreTimeZonePlugin)) === false) {
        store.addPlugins(StoreTimeZonePlugin);
      }
    }
    unplugStore(store) {
      var _a6;
      (_a6 = store.plugins.storeTimeZonePlugin) == null ? void 0 : _a6.destroy();
    }
    attachStore(store) {
      super.attachStore(store);
      if (store && this.data && this.timeZone != null && this._storesWithDates.includes(store)) {
        this.plugStore(store);
        this.convertStoresToTimeZone([store]);
      }
    }
    detachStore(store) {
      super.detachStore(store);
      if (store && !store.isDestroyed && this.timeZone != null) {
        this.convertStoresToTimeZone([store], null);
        this.unplugStore(store);
      }
    }
    relayStoreChange({ source, action, records, replaced }) {
      const me = this;
      if (me.timeZone != null && me._storesWithDates.includes(source)) {
        if (["add", "replace"].includes(action)) {
          if (!(records == null ? void 0 : records.length) && (replaced == null ? void 0 : replaced.length)) {
            records = replaced;
          }
          if (records.length) {
            records.forEach((record) => record.timeZone = me.timeZone);
          }
        }
      }
    }
    convertStoresToTimeZone(stores2, timeZone = this.timeZone) {
      var _a6;
      const { stm } = this, disableStm = stm == null ? void 0 : stm.enabled;
      if (disableStm) {
        stm.disabled = true;
      }
      for (const store of stores2) {
        (_a6 = store == null ? void 0 : store.allRecords) == null ? void 0 : _a6.forEach((r) => r.convertToTimeZone(timeZone));
      }
      if (disableStm) {
        this.commitAsync().then(() => {
          stm.disabled = false;
        });
      }
    }
    updateTimeZone(timeZone, oldTimeZone) {
      var _a6, _b2;
      const me = this, isConfiguring = me._isConfiguringTimeZone || me.isConfiguring;
      me.trigger("beforeTimeZoneChange", {
        timeZone,
        oldTimeZone,
        isConfiguring
      });
      (_b2 = (_a6 = me.calendarManagerStore).forEach) == null ? void 0 : _b2.call(_a6, (calendar) => calendar.bumpVersion());
      me._storesWithDates.forEach((store) => me.plugStore(store));
      me.convertStoresToTimeZone(me._storesWithDates);
      if (me.startDate) {
        const startDate = oldTimeZone != null ? TimeZoneHelper.fromTimeZone(me.startDate, oldTimeZone) : me.startDate;
        me.startDate = timeZone != null ? TimeZoneHelper.toTimeZone(startDate, timeZone) : startDate;
      }
      me.ignoreRecordChanges = true;
      me.commitAsync().then(() => {
        if (!me.isDestroyed) {
          me.trigger("timeZoneChange", {
            timeZone,
            oldTimeZone,
            isConfiguring
          });
        }
        delete me._isConfiguringTimeZone;
      });
    }
  }, __publicField(_a5, "$name", "ProjectModelTimeZoneMixin"), __publicField(_a5, "configurable", {
    /**
     * Set to a IANA time zone (i.e. `Europe/Stockholm`) or a UTC offset in minutes (i.e. `-120`). This will
     * convert all events, tasks and time ranges to the specified time zone or offset. It will also affect the
     * displayed timeline's headers as well at the start and end date of it.
     *
     * There is currently no built-in time zone support in JavaScript which means that the converted dates
     * technically still are in the local system time zone, but adjusted to match the configured time zone.
     *
     * ### DST
     * If a IANA time zone is provided, there will be support for DST. But if local system time zone has DST that
     * will affect the time zone conversion at the exact hour when the local system time zone switches DST on and
     * off.
     *
     * *For example:*
     * 1. The local system time zone is `Europe/Stockholm` (which is UTC+1 or UTC+2 when DST).
     * 2. The date `2022-03-27T07:00:00Z` (which is UTC) is converted to `America/Chicago` (which is UTC-6 or UTC-5
     *    when DST).
     * 3. The converted JS date will be created from `2022-03-27T02:00:00` which is exactly the hour when
     *    `Europe/Stockholm` adds an DST hour. This has the effect that the converted date shows up incorrectly as
     *    `2022-03-27T03:00` instead.
     *
     * If a UTC offset is provided, there is no DST support at all.
     *
     * ### Editing
     * If creating new records or editing existing record dates, the dates will be interpreted as in the selected
     * time zone.
     *
     * If you want to create new records with dates that either should be interpreted as local system time zone or
     * from any other time zone, specify the {@link Scheduler.model.mixin.TimeZonedDatesMixin#field-timeZone} field
     * on the record.
     *
     * ### Saving
     * When saving or syncing data, the dates will be restored to local system time and converted to JSON
     * ISO formatted. When restoring, it adds or subtracts the time difference accordingly.
     * For instance, if the current timezone is `UTC` and you're in UTC +3, 3 hours will be added to the time.
     *
     * The `store.toJSON()` and `model.toJSON()` methods also restores the data. For example, `eventStore.toJSON()`
     * will show the dates in local timezone.
     *
     * @prp {String|Number} [timeZone]
     * @category Advanced
     */
    timeZone: {
      // Don't ingest the config eagerly because it relies on project being present.
      // Lazy means it waits for ingestion until timeZone property is referenced.
      $config: "lazy",
      value: null
    }
  }), _a5;
};

// ../Scheduler/lib/Scheduler/model/TimeRangeModel.js
var TimeRangeModel = class extends TimeSpan.mixin(RecurringTimeSpan_default) {
  /**
   * @hidefields children, parentId, parentIndex
   */
  afterConstruct() {
    if (!this.endDate) {
      this.endDate = this.startDate;
    }
    super.afterConstruct();
  }
};
__publicField(TimeRangeModel, "$name", "TimeRangeModel");
TimeRangeModel._$name = "TimeRangeModel";

// ../Scheduler/lib/Scheduler/data/TimeRangeStore.js
var TimeRangeStore = class extends AjaxStore.mixin(RecurringTimeSpansMixin_default) {
  get $lazyLoadPluginClass() {
    return this.isPaged ? PagedDateStoreLazyLoadPlugin : DateStoreLazyLoadPlugin;
  }
};
__publicField(TimeRangeStore, "$name", "TimeRangeStore");
__publicField(TimeRangeStore, "defaultConfig", {
  /**
   * CrudManager must load stores in the correct order. Lowest first.
   * @private
   */
  loadPriority: 500,
  /**
   * CrudManager must sync stores in the correct order. Lowest first.
   * @private
   */
  syncPriority: 500,
  modelClass: TimeRangeModel,
  id: "timeRanges"
});
TimeRangeStore._$name = "TimeRangeStore";

// ../Scheduler/lib/Scheduler/model/mixin/ProjectModelMixin.js
var ProjectModelMixin_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Model).mixin(
    ProjectModelCommon_default,
    ProjectModelTimeZoneMixin_default
  ) {
    //region Config
    static get defaultConfig() {
      return {
        /**
         * State tracking manager instance the project relies on
         * @member {Core.data.stm.StateTrackingManager} stm
         * @category Advanced
         */
        /**
         * Configuration options to provide to the STM manager
         *
         * @config {StateTrackingManagerConfig|Core.data.stm.StateTrackingManager}
         * @category Advanced
         */
        stm: {},
        timeRangeModelClass: TimeRangeModel,
        resourceTimeRangeModelClass: ResourceTimeRangeModel,
        /**
         * The constructor to create a time range store instance with. Should be a class subclassing the
         * {@link Scheduler.data.TimeRangeStore}
         * @config {Scheduler.data.TimeRangeStore|Object}
         * @typings {typeof TimeRangeStore|object}
         * @category Models & Stores
         */
        timeRangeStoreClass: TimeRangeStore,
        /**
         * The constructor to create a resource time range store instance with. Should be a class subclassing the
         * {@link Scheduler.data.ResourceTimeRangeStore}
         * @config {Scheduler.data.ResourceTimeRangeStore|Object}
         * @typings {typeof ResourceTimeRangeStore|object}
         * @category Models & Stores
         */
        resourceTimeRangeStoreClass: ResourceTimeRangeStore,
        /**
         * The initial data, to fill the {@link #property-timeRangeStore timeRangeStore} with.
         * Should be an array of {@link Scheduler.model.TimeSpan TimeSpan} or its configuration objects.
         *
         * @config {Scheduler.model.TimeSpan[]} [timeRangesData]
         * @category Legacy inline data
         */
        /**
         * The initial data, to fill the {@link #property-resourceTimeRangeStore resourceTimeRangeStore} with.
         * Should be an array of {@link Scheduler.model.ResourceTimeRangeModel ResourceTimeRangeModel} or it's
         * configuration objects.
         *
         * @config {Scheduler.model.ResourceTimeRangeModel[]} [resourceTimeRangesData]
         * @category Legacy inline data
         */
        eventStore: {},
        assignmentStore: {},
        dependencyStore: {},
        resourceStore: {},
        timeRangesData: null,
        resourceTimeRangesData: null
      };
    }
    //endregion
    //region Functions
    /**
     * Accepts a "data package" consisting of data for the projects stores, which is then loaded into the stores.
     *
     * The package can hold data for `EventStore`, `AssignmentStore`, `ResourceStore`, `DependencyStore`,
     * `TimeRangeStore` and `ResourceTimeRangeStore`. It uses the same format as when creating a project with inline
     * data:
     *
     * ```javascript
     * await project.loadInlineData({
     *     eventsData : [
     *         { id : 1, name : 'Proof-read docs', startDate : '2017-01-02', endDate : '2017-01-09' },
     *         { id : 2, name : 'Release docs', startDate : '2017-01-09', endDate : '2017-01-10' }
     *     ],
     *     resourcesData : [
     *         { id : 1, name : 'Arcady' },
     *         { id : 2, name : 'Don' }
     *     ],
     *     dependenciesData : [
     *         { fromEvent : 1, toEvent : 2 }
     *     ],
     *     assignmentsData : [
     *          { 'event' : 1, 'resource' : 1 },
     *          { 'event' : 2, 'resource' : 2 }
     *      ],
     *     resourceTimeRangesData : [
     *         { id : 1, name : 'Resource range', resourceId : 1, startDate : '2017-01-08', endDate : '2017-01-10' }
     *     ],
     *     timeRangesData : [
     *          { id : 1, startDate : '2017-01-26', name : 'Cool line' }
     *     ]
     * });
     * ```
     *
     * After populating the stores it commits the project, starting its calculations. By awaiting `loadInlineData()` you
     * can be sure that project calculations are finished.
     *
     * @function loadInlineData
     * @param {Object} dataPackage A data package as described above
     * @async
     * @category Inline data
     */
    /**
     * Project changes (CRUD operations to records in its stores) are automatically committed on a buffer to the
     * underlying graph based calculation engine. The engine performs it calculations async.
     *
     * By calling this function, the commit happens right away. And by awaiting it you are sure that project
     * calculations are finished and that references between records are up to date.
     *
     * The returned promise is resolved with an object. If that object has `rejectedWith` set, there has been a conflict and the calculation failed.
     *
     * ```javascript
     * // Move an event in time
     * eventStore.first.shift(1);
     *
     * // Trigger calculations directly and wait for them to finish
     * const result = await project.commitAsync();
     *
     * if (result.rejectedWith) {
     *     // there was a conflict during the scheduling
     * }
     * ```
     *
     * @async
     * @function commitAsync
     * @category Common
     */
    //endregion
    //region Init
    construct(config = {}) {
      super.construct(...arguments);
      if (config.timeRangesData) {
        this.timeRangeStore.data = config.timeRangesData;
      }
      if (config.resourceTimeRangesData) {
        this.resourceTimeRangeStore.data = config.resourceTimeRangesData;
      }
    }
    afterConstruct() {
      super.afterConstruct();
      const me = this;
      !me.timeRangeStore.stm && me.stm.addStore(me.timeRangeStore);
      !me.resourceTimeRangeStore.stm && me.stm.addStore(me.resourceTimeRangeStore);
    }
    //endregion
    //region Attaching stores
    // Attach to a store, relaying its change events
    attachStore(store) {
      if (store) {
        store.ion({
          name: store.$$name,
          change: "relayStoreChange",
          thisObj: this
        });
      }
      super.attachStore(store);
    }
    // Detach a store, stop relaying its change events
    detachStore(store) {
      if (store) {
        this.detachListeners(store.$$name);
        super.detachStore(store);
      }
    }
    relayStoreChange(event) {
      super.relayStoreChange(event);
      return this.trigger("change", { store: event.source, ...event, source: this });
    }
    updateTimeRangeStore(store, oldStore) {
      this.detachStore(oldStore);
      this.attachStore(store);
      if (oldStore) {
        oldStore.project = null;
      }
      if (store) {
        store.project = this;
      }
    }
    setTimeRangeStore(store) {
      this.timeRangeStore = store;
    }
    changeTimeRangeStore(store) {
      if (store && !store.isStore) {
        store = this.timeRangeStoreClass.new({
          modelClass: this.timeRangeModelClass
        }, store);
      }
      return store;
    }
    updateResourceTimeRangeStore(store, oldStore) {
      this.detachStore(oldStore);
      this.attachStore(store);
      if (oldStore) {
        oldStore.project = null;
      }
      if (store) {
        store.project = this;
      }
    }
    changeResourceTimeRangeStore(store) {
      if (store && !store.isStore) {
        store = this.resourceTimeRangeStoreClass.new({
          modelClass: this.resourceTimeRangeModelClass
        }, store);
      }
      return store;
    }
    setResourceTimeRangeStore(store) {
      this.resourceTimeRangeStore = store;
    }
    //endregion
    //region Inline data
    get events() {
      return this.eventStore.allRecords;
    }
    updateEvents(events) {
      this.eventStore.data = events;
    }
    get resourceTimeRanges() {
      return this.resourceTimeRangeStore.allRecords;
    }
    updateResourceTimeRanges(resourceTimeRanges) {
      this.resourceTimeRangeStore.data = resourceTimeRanges;
    }
    async loadInlineData(data) {
      this.isLoadingInlineData = true;
      if (data.resourceTimeRangesData) {
        this.resourceTimeRangeStore.data = data.resourceTimeRangesData;
      }
      if (data.timeRangesData) {
        this.timeRangeStore.data = data.timeRangesData;
      }
      return super.loadInlineData(data);
    }
    //endregion
    afterChange(toSet, wasSet) {
      super.afterChange(...arguments);
      if (wasSet.calendar) {
        this.trigger("calendarChange");
      }
    }
    doDestroy() {
      this.timeRangeStore.destroy();
      this.resourceTimeRangeStore.destroy();
      super.doDestroy();
    }
  }, __publicField(_a5, "$name", "ProjectModelMixin"), __publicField(_a5, "configurable", {
    /**
     * The {@link Core.data.Store store} holding the time ranges information.
     *
     * See also {@link Scheduler.model.TimeSpan}
     *
     * @member {Core.data.Store} timeRangeStore
     * @category Models & Stores
     */
    /**
     * A {@link Core.data.Store} instance or a config object.
     * @config {Core.data.Store|StoreConfig}
     * @category Models & Stores
     */
    timeRangeStore: {
      value: {},
      $config: "nullify"
    },
    /**
     * The {@link Scheduler.data.ResourceTimeRangeStore store} holding the resource time ranges information.
     *
     * See also {@link Scheduler.model.ResourceTimeRangeModel}
     *
     * @member {Scheduler.data.ResourceTimeRangeStore} resourceTimeRangeStore
     * @category Models & Stores
     */
    /**
     * A {@link Scheduler.data.ResourceTimeRangeStore} instance or a config object.
     * @config {Scheduler.data.ResourceTimeRangeStore|ResourceTimeRangeStoreConfig}
     * @category Models & Stores
     */
    resourceTimeRangeStore: {
      value: {},
      $config: "nullify"
    },
    // Documented in Scheduler/SchedulerPro versions of model/ProjectModel since types differ
    events: null,
    resourceTimeRanges: null
  }), _a5;
};

// ../Scheduler/lib/Scheduler/model/mixin/ProjectCurrentConfig.js
var ProjectCurrentConfig_default = (Target) => class ProjectCurrentConfig extends Target {
  // This function is not meant to be called by any code other than Base#getCurrentConfig().
  // It extracts the current configs/fields for the project, with special handling for inline data
  getCurrentConfig(options) {
    const me = this, result = super.getCurrentConfig(options);
    if (result) {
      for (const storeName of ["eventStore", "resourceStore", "assignmentStore", "dependencyStore", "timeRangeStore", "resourceTimeRangeStore"]) {
        const store = me[storeName];
        if (store) {
          if (store.count) {
            result[store.id + "Data"] = store.getInlineData(options);
          }
          const storeState = store.getCurrentConfig(options);
          if (storeState && Object.keys(storeState).length > 0) {
            if (Object.keys(storeState).length === 1 && storeState.data) {
              delete result[storeName];
            } else {
              result[storeName] = Object.assign(result[storeName] || {}, storeState);
            }
          } else if (result[storeName] && Object.keys(result[storeName]).length === 0) {
            delete result[storeName];
          }
        }
      }
      if (me.taskStore.isTaskStore) {
        delete result.eventModelClass;
        delete result.eventStoreClass;
        delete result.children;
      }
      return result;
    }
  }
};

// ../Scheduler/lib/Scheduler/data/util/ModelPersistencyManager.js
var ModelPersistencyManager = class extends Base {
  // region Event attachers
  set eventStore(newEventStore) {
    const me = this;
    me.eventStoreDetacher && me.eventStoreDetacher();
    me._eventStore = newEventStore;
    if (newEventStore && newEventStore.autoCommit) {
      me.eventStoreDetacher = newEventStore.ion({
        beforecommit: me.onEventStoreBeforeSync,
        thisObj: me,
        detachable: true,
        // Just in case
        prio: 100
      });
    }
  }
  get eventStore() {
    return this._eventStore;
  }
  set resourceStore(newResourceStore) {
    const me = this;
    me.resourceStoreDetacher && me.resourceStoreDetacher();
    me._resourceStore = newResourceStore;
    if (newResourceStore && newResourceStore.autoCommit) {
      me.resourceStoreDetacher = newResourceStore.ion({
        beforecommit: me.onResourceStoreBeforeSync,
        thisObj: me,
        detachable: true,
        // Just in case
        prio: 100
      });
    }
  }
  get resourceStore() {
    return this._resourceStore;
  }
  set assignmentStore(newAssignmentStore) {
    const me = this;
    me.assignmentStoreDetacher && me.assignmentStoreDetacher();
    me._assignmentStore = newAssignmentStore;
    if (newAssignmentStore && newAssignmentStore.autoSync) {
      me.assignmentStoreDetacher = newAssignmentStore.ion({
        beforecommit: me.onAssignmentStoreBeforeSync,
        thisObj: me,
        detachable: true,
        // Just in case
        prio: 100
      });
    }
  }
  get assignmentStore() {
    return this._assignmentStore;
  }
  set dependencyStore(newDependencyStore) {
    const me = this;
    me.dependencyStoreDetacher && me.dependencyStoreDetacher();
    me._dependencyStore = newDependencyStore;
    if (newDependencyStore && newDependencyStore.autoSync) {
      me.dependencyStoreDetacher = newDependencyStore.ion({
        beforecommit: me.onDependencyStoreBeforeSync,
        thisObj: me,
        detachable: true,
        // Just in case
        prio: 100
      });
    }
  }
  get dependencyStore() {
    return this._dependencyStore;
  }
  // endregion
  // region Event handlers
  onEventStoreBeforeSync({ changes }) {
    const me = this;
    me.removeNonPersistableRecordsToCreate(changes);
    return me.shallContinueSync(changes);
  }
  onResourceStoreBeforeSync({ changes }) {
    const me = this;
    me.removeNonPersistableRecordsToCreate(changes);
    return me.shallContinueSync(changes);
  }
  onAssignmentStoreBeforeSync({ changes }) {
    const me = this;
    me.removeNonPersistableRecordsToCreate(changes);
    return me.shallContinueSync(changes);
  }
  onDependencyStoreBeforeSync({ changes }) {
    const me = this;
    me.removeNonPersistableRecordsToCreate(changes);
    return me.shallContinueSync(changes);
  }
  // endregion
  // region Management rules
  removeNonPersistableRecordsToCreate(changes) {
    const recordsToCreate = changes.added || [];
    let r, i;
    for (i = recordsToCreate.length - 1; i >= 0; --i) {
      r = recordsToCreate[i];
      if (!r.isPersistable) {
        recordsToCreate.splice(recordsToCreate.indexOf(r), 1);
      }
    }
    if (recordsToCreate.length === 0) {
      changes.added.length = 0;
    }
  }
  shallContinueSync(options) {
    return Boolean(options.added && options.added.length > 0 || options.modified && options.modified.length > 0 || options.removed && options.removed.length > 0);
  }
  // endregion
};
ModelPersistencyManager._$name = "ModelPersistencyManager";

// ../Engine/lib/Engine/vendor/later/later.js
var diffSecond = (date2, diff) => {
  return new Date(
    date2.getFullYear(),
    date2.getMonth(),
    date2.getDate(),
    date2.getHours(),
    date2.getMinutes(),
    date2.getSeconds() + diff,
    date2.getMilliseconds()
  );
};
var later = function() {
  "use strict";
  var later2 = {
    version: "1.2.0"
  };
  if (!Array.prototype.indexOf) {
    Array.prototype.indexOf = function(searchElement) {
      "use strict";
      if (this == null) {
        throw new TypeError();
      }
      var t = Object(this);
      var len = t.length >>> 0;
      if (len === 0) {
        return -1;
      }
      var n = 0;
      if (arguments.length > 1) {
        n = Number(arguments[1]);
        if (n != n) {
          n = 0;
        } else if (n != 0 && n != Infinity && n != -Infinity) {
          n = (n > 0 || -1) * Math.floor(Math.abs(n));
        }
      }
      if (n >= len) {
        return -1;
      }
      var k = n >= 0 ? n : Math.max(len - Math.abs(n), 0);
      for (; k < len; k++) {
        if (k in t && t[k] === searchElement) {
          return k;
        }
      }
      return -1;
    };
  }
  if (!String.prototype.trim) {
    String.prototype.trim = function() {
      return this.replace(/^\s+|\s+$/g, "");
    };
  }
  later2.array = {};
  later2.array.sort = function(arr, zeroIsLast) {
    arr.sort(function(a, b) {
      return +a - +b;
    });
    if (zeroIsLast && arr[0] === 0) {
      arr.push(arr.shift());
    }
  };
  later2.array.next = function(val, values, extent) {
    var cur, zeroIsLargest = extent[0] !== 0, nextIdx = 0;
    for (var i = values.length - 1; i > -1; --i) {
      cur = values[i];
      if (cur === val) {
        return cur;
      }
      if (cur > val || cur === 0 && zeroIsLargest && extent[1] > val) {
        nextIdx = i;
        continue;
      }
      break;
    }
    return values[nextIdx];
  };
  later2.array.nextInvalid = function(val, values, extent) {
    var min4 = extent[0], max3 = extent[1], len = values.length, zeroVal = values[len - 1] === 0 && min4 !== 0 ? max3 : 0, next = val, i = values.indexOf(val), start = next;
    while (next === (values[i] || zeroVal)) {
      next++;
      if (next > max3) {
        next = min4;
      }
      i++;
      if (i === len) {
        i = 0;
      }
      if (next === start) {
        return void 0;
      }
    }
    return next;
  };
  later2.array.prev = function(val, values, extent) {
    var cur, len = values.length, zeroIsLargest = extent[0] !== 0, prevIdx = len - 1;
    for (var i = 0; i < len; i++) {
      cur = values[i];
      if (cur === val) {
        return cur;
      }
      if (cur < val || cur === 0 && zeroIsLargest && extent[1] < val) {
        prevIdx = i;
        continue;
      }
      break;
    }
    return values[prevIdx];
  };
  later2.array.prevInvalid = function(val, values, extent) {
    var min4 = extent[0], max3 = extent[1], len = values.length, zeroVal = values[len - 1] === 0 && min4 !== 0 ? max3 : 0, next = val, i = values.indexOf(val), start = next;
    while (next === (values[i] || zeroVal)) {
      next--;
      if (next < min4) {
        next = max3;
      }
      i--;
      if (i === -1) {
        i = len - 1;
      }
      if (next === start) {
        return void 0;
      }
    }
    return next;
  };
  later2.day = later2.D = {
    name: "day",
    range: 86400,
    val: function(d) {
      return d.D || (d.D = later2.date.getDate.call(d));
    },
    isValid: function(d, val) {
      return later2.D.val(d) === (val || later2.D.extent(d)[1]);
    },
    extent: function(d) {
      if (d.DExtent)
        return d.DExtent;
      var month2 = later2.M.val(d), max3 = later2.DAYS_IN_MONTH[month2 - 1];
      if (month2 === 2 && later2.dy.extent(d)[1] === 366) {
        max3 = max3 + 1;
      }
      return d.DExtent = [1, max3];
    },
    start: function(d) {
      return d.DStart || (d.DStart = later2.date.next(later2.Y.val(d), later2.M.val(d), later2.D.val(d)));
    },
    end: function(d) {
      return d.DEnd || (d.DEnd = later2.date.prev(later2.Y.val(d), later2.M.val(d), later2.D.val(d)));
    },
    next: function(d, val) {
      val = val > later2.D.extent(d)[1] ? 1 : val;
      var month2 = later2.date.nextRollover(d, val, later2.D, later2.M), DMax = later2.D.extent(month2)[1];
      val = val > DMax ? 1 : val || DMax;
      return later2.date.next(later2.Y.val(month2), later2.M.val(month2), val);
    },
    prev: function(d, val) {
      var month2 = later2.date.prevRollover(d, val, later2.D, later2.M), DMax = later2.D.extent(month2)[1];
      return later2.date.prev(later2.Y.val(month2), later2.M.val(month2), val > DMax ? DMax : val || DMax);
    }
  };
  later2.dayOfWeekCount = later2.dc = {
    name: "day of week count",
    range: 604800,
    val: function(d) {
      return d.dc || (d.dc = Math.floor((later2.D.val(d) - 1) / 7) + 1);
    },
    isValid: function(d, val) {
      return later2.dc.val(d) === val || val === 0 && later2.D.val(d) > later2.D.extent(d)[1] - 7;
    },
    extent: function(d) {
      return d.dcExtent || (d.dcExtent = [1, Math.ceil(later2.D.extent(d)[1] / 7)]);
    },
    start: function(d) {
      return d.dcStart || (d.dcStart = later2.date.next(later2.Y.val(d), later2.M.val(d), Math.max(1, (later2.dc.val(d) - 1) * 7 + 1 || 1)));
    },
    end: function(d) {
      return d.dcEnd || (d.dcEnd = later2.date.prev(later2.Y.val(d), later2.M.val(d), Math.min(later2.dc.val(d) * 7, later2.D.extent(d)[1])));
    },
    next: function(d, val) {
      val = val > later2.dc.extent(d)[1] ? 1 : val;
      var month2 = later2.date.nextRollover(d, val, later2.dc, later2.M), dcMax = later2.dc.extent(month2)[1];
      val = val > dcMax ? 1 : val;
      var next = later2.date.next(later2.Y.val(month2), later2.M.val(month2), val === 0 ? later2.D.extent(month2)[1] - 6 : 1 + 7 * (val - 1));
      if (next.getTime() <= d.getTime()) {
        month2 = later2.M.next(d, later2.M.val(d) + 1);
        return later2.date.next(later2.Y.val(month2), later2.M.val(month2), val === 0 ? later2.D.extent(month2)[1] - 6 : 1 + 7 * (val - 1));
      }
      return next;
    },
    prev: function(d, val) {
      var month2 = later2.date.prevRollover(d, val, later2.dc, later2.M), dcMax = later2.dc.extent(month2)[1];
      val = val > dcMax ? dcMax : val || dcMax;
      return later2.dc.end(later2.date.prev(later2.Y.val(month2), later2.M.val(month2), 1 + 7 * (val - 1)));
    }
  };
  later2.dayOfWeek = later2.dw = later2.d = {
    name: "day of week",
    range: 86400,
    val: function(d) {
      return d.dw || (d.dw = later2.date.getDay.call(d) + 1);
    },
    isValid: function(d, val) {
      return later2.dw.val(d) === (val || 7);
    },
    extent: function() {
      return [1, 7];
    },
    start: function(d) {
      return later2.D.start(d);
    },
    end: function(d) {
      return later2.D.end(d);
    },
    next: function(d, val) {
      val = val > 7 ? 1 : val || 7;
      return later2.date.next(later2.Y.val(d), later2.M.val(d), later2.D.val(d) + (val - later2.dw.val(d)) + (val <= later2.dw.val(d) ? 7 : 0));
    },
    prev: function(d, val) {
      val = val > 7 ? 7 : val || 7;
      return later2.date.prev(later2.Y.val(d), later2.M.val(d), later2.D.val(d) + (val - later2.dw.val(d)) + (val >= later2.dw.val(d) ? -7 : 0));
    }
  };
  later2.dayOfYear = later2.dy = {
    name: "day of year",
    range: 86400,
    val: function(d) {
      return d.dy || (d.dy = Math.ceil(1 + (later2.D.start(d).getTime() - later2.Y.start(d).getTime()) / later2.DAY));
    },
    isValid: function(d, val) {
      return later2.dy.val(d) === (val || later2.dy.extent(d)[1]);
    },
    extent: function(d) {
      var year = later2.Y.val(d);
      return d.dyExtent || (d.dyExtent = [1, year % 4 ? 365 : 366]);
    },
    start: function(d) {
      return later2.D.start(d);
    },
    end: function(d) {
      return later2.D.end(d);
    },
    next: function(d, val) {
      val = val > later2.dy.extent(d)[1] ? 1 : val;
      var year = later2.date.nextRollover(d, val, later2.dy, later2.Y), dyMax = later2.dy.extent(year)[1];
      val = val > dyMax ? 1 : val || dyMax;
      return later2.date.next(later2.Y.val(year), later2.M.val(year), val);
    },
    prev: function(d, val) {
      var year = later2.date.prevRollover(d, val, later2.dy, later2.Y), dyMax = later2.dy.extent(year)[1];
      val = val > dyMax ? dyMax : val || dyMax;
      return later2.date.prev(later2.Y.val(year), later2.M.val(year), val);
    }
  };
  later2.hour = later2.h = {
    name: "hour",
    range: 3600,
    val: function(d) {
      return d.h || (d.h = later2.date.getHour.call(d));
    },
    isValid: function(d, val) {
      return later2.h.val(d) === val;
    },
    extent: function() {
      return [0, 23];
    },
    start: function(d) {
      return d.hStart || (d.hStart = later2.date.next(later2.Y.val(d), later2.M.val(d), later2.D.val(d), later2.h.val(d)));
    },
    end: function(d) {
      return d.hEnd || (d.hEnd = later2.date.prev(later2.Y.val(d), later2.M.val(d), later2.D.val(d), later2.h.val(d)));
    },
    next: function(d, val) {
      val = val > 23 ? 0 : val;
      var next = later2.date.next(later2.Y.val(d), later2.M.val(d), later2.D.val(d) + (val <= later2.h.val(d) ? 1 : 0), val);
      if (!later2.date.isUTC && next.getTime() <= d.getTime()) {
        next = later2.date.next(later2.Y.val(next), later2.M.val(next), later2.D.val(next), val + 1);
      }
      return next;
    },
    prev: function(d, val) {
      val = val > 23 ? 23 : val;
      return later2.date.prev(later2.Y.val(d), later2.M.val(d), later2.D.val(d) + (val >= later2.h.val(d) ? -1 : 0), val);
    }
  };
  later2.minute = later2.m = {
    name: "minute",
    range: 60,
    val: function(d) {
      return d.m || (d.m = later2.date.getMin.call(d));
    },
    isValid: function(d, val) {
      return later2.m.val(d) === val;
    },
    extent: function(d) {
      return [0, 59];
    },
    start: function(d) {
      return d.mStart || (d.mStart = later2.date.next(later2.Y.val(d), later2.M.val(d), later2.D.val(d), later2.h.val(d), later2.m.val(d)));
    },
    end: function(d) {
      return d.mEnd || (d.mEnd = later2.date.prev(later2.Y.val(d), later2.M.val(d), later2.D.val(d), later2.h.val(d), later2.m.val(d)));
    },
    next: function(d, val) {
      var m = later2.m.val(d), s = later2.s.val(d), inc = val > 59 ? 60 - m : val <= m ? 60 - m + val : val - m, next = new Date(d.getTime() + inc * later2.MIN - s * later2.SEC);
      if (!later2.date.isUTC && next.getTime() <= d.getTime()) {
        next = new Date(d.getTime() + (inc + 120) * later2.MIN - s * later2.SEC);
      }
      return next;
    },
    prev: function(d, val) {
      val = val > 59 ? 59 : val;
      return later2.date.prev(later2.Y.val(d), later2.M.val(d), later2.D.val(d), later2.h.val(d) + (val >= later2.m.val(d) ? -1 : 0), val);
    }
  };
  later2.month = later2.M = {
    name: "month",
    range: 2629740,
    val: function(d) {
      return d.M || (d.M = later2.date.getMonth.call(d) + 1);
    },
    isValid: function(d, val) {
      return later2.M.val(d) === (val || 12);
    },
    extent: function() {
      return [1, 12];
    },
    start: function(d) {
      return d.MStart || (d.MStart = later2.date.next(later2.Y.val(d), later2.M.val(d)));
    },
    end: function(d) {
      return d.MEnd || (d.MEnd = later2.date.prev(later2.Y.val(d), later2.M.val(d)));
    },
    next: function(d, val) {
      val = val > 12 ? 1 : val || 12;
      return later2.date.next(later2.Y.val(d) + (val > later2.M.val(d) ? 0 : 1), val);
    },
    prev: function(d, val) {
      val = val > 12 ? 12 : val || 12;
      return later2.date.prev(later2.Y.val(d) - (val >= later2.M.val(d) ? 1 : 0), val);
    }
  };
  later2.second = later2.s = {
    name: "second",
    range: 1,
    val: function(d) {
      return d.s || (d.s = later2.date.getSec.call(d));
    },
    isValid: function(d, val) {
      return later2.s.val(d) === val;
    },
    extent: function() {
      return [0, 59];
    },
    start: function(d) {
      return d;
    },
    end: function(d) {
      return d;
    },
    next: function(d, val) {
      var s = later2.s.val(d), inc = val > 59 ? 60 - s : val <= s ? 60 - s + val : val - s, next = new Date(d.getTime() + inc * later2.SEC);
      if (!later2.date.isUTC && next.getTime() <= d.getTime()) {
        next = new Date(d.getTime() + (inc + 7200) * later2.SEC);
      }
      return next;
    },
    prev: function(d, val, cache) {
      val = val > 59 ? 59 : val;
      return later2.date.prev(later2.Y.val(d), later2.M.val(d), later2.D.val(d), later2.h.val(d), later2.m.val(d) + (val >= later2.s.val(d) ? -1 : 0), val);
    }
  };
  later2.time = later2.t = {
    name: "time",
    range: 1,
    val: function(d) {
      return d.t || (d.t = later2.h.val(d) * 3600 + later2.m.val(d) * 60 + later2.s.val(d));
    },
    isValid: function(d, val) {
      return later2.t.val(d) === val;
    },
    extent: function() {
      return [0, 86399];
    },
    start: function(d) {
      return d;
    },
    end: function(d) {
      return d;
    },
    next: function(d, val) {
      val = val > 86399 ? 0 : val;
      var next = later2.date.next(later2.Y.val(d), later2.M.val(d), later2.D.val(d) + (val <= later2.t.val(d) ? 1 : 0), 0, 0, val);
      if (!later2.date.isUTC && next.getTime() < d.getTime()) {
        next = later2.date.next(later2.Y.val(next), later2.M.val(next), later2.D.val(next), later2.h.val(next), later2.m.val(next), val + 7200);
      }
      return next;
    },
    prev: function(d, val) {
      val = val > 86399 ? 86399 : val;
      return later2.date.next(later2.Y.val(d), later2.M.val(d), later2.D.val(d) + (val >= later2.t.val(d) ? -1 : 0), 0, 0, val);
    }
  };
  later2.weekOfMonth = later2.wm = {
    name: "week of month",
    range: 604800,
    val: function(d) {
      return d.wm || (d.wm = (later2.D.val(d) + (later2.dw.val(later2.M.start(d)) - 1) + (7 - later2.dw.val(d))) / 7);
    },
    isValid: function(d, val) {
      return later2.wm.val(d) === (val || later2.wm.extent(d)[1]);
    },
    extent: function(d) {
      return d.wmExtent || (d.wmExtent = [1, (later2.D.extent(d)[1] + (later2.dw.val(later2.M.start(d)) - 1) + (7 - later2.dw.val(later2.M.end(d)))) / 7]);
    },
    start: function(d) {
      return d.wmStart || (d.wmStart = later2.date.next(later2.Y.val(d), later2.M.val(d), Math.max(later2.D.val(d) - later2.dw.val(d) + 1, 1)));
    },
    end: function(d) {
      return d.wmEnd || (d.wmEnd = later2.date.prev(later2.Y.val(d), later2.M.val(d), Math.min(later2.D.val(d) + (7 - later2.dw.val(d)), later2.D.extent(d)[1])));
    },
    next: function(d, val) {
      val = val > later2.wm.extent(d)[1] ? 1 : val;
      var month2 = later2.date.nextRollover(d, val, later2.wm, later2.M), wmMax = later2.wm.extent(month2)[1];
      val = val > wmMax ? 1 : val || wmMax;
      return later2.date.next(later2.Y.val(month2), later2.M.val(month2), Math.max(1, (val - 1) * 7 - (later2.dw.val(month2) - 2)));
    },
    prev: function(d, val) {
      var month2 = later2.date.prevRollover(d, val, later2.wm, later2.M), wmMax = later2.wm.extent(month2)[1];
      val = val > wmMax ? wmMax : val || wmMax;
      return later2.wm.end(later2.date.next(later2.Y.val(month2), later2.M.val(month2), Math.max(1, (val - 1) * 7 - (later2.dw.val(month2) - 2))));
    }
  };
  later2.weekOfYear = later2.wy = {
    name: "week of year (ISO)",
    range: 604800,
    val: function(d) {
      if (d.wy)
        return d.wy;
      var wThur = later2.dw.next(later2.wy.start(d), 5), YThur = later2.dw.next(later2.Y.prev(wThur, later2.Y.val(wThur) - 1), 5);
      return d.wy = 1 + Math.ceil((wThur.getTime() - YThur.getTime()) / later2.WEEK);
    },
    isValid: function(d, val) {
      return later2.wy.val(d) === (val || later2.wy.extent(d)[1]);
    },
    extent: function(d) {
      if (d.wyExtent)
        return d.wyExtent;
      var year = later2.dw.next(later2.wy.start(d), 5), dwFirst = later2.dw.val(later2.Y.start(year)), dwLast = later2.dw.val(later2.Y.end(year));
      return d.wyExtent = [1, dwFirst === 5 || dwLast === 5 ? 53 : 52];
    },
    start: function(d) {
      return d.wyStart || (d.wyStart = later2.date.next(later2.Y.val(d), later2.M.val(d), later2.D.val(d) - (later2.dw.val(d) > 1 ? later2.dw.val(d) - 2 : 6)));
    },
    end: function(d) {
      return d.wyEnd || (d.wyEnd = later2.date.prev(later2.Y.val(d), later2.M.val(d), later2.D.val(d) + (later2.dw.val(d) > 1 ? 8 - later2.dw.val(d) : 0)));
    },
    next: function(d, val) {
      val = val > later2.wy.extent(d)[1] ? 1 : val;
      var wyThur = later2.dw.next(later2.wy.start(d), 5), year = later2.date.nextRollover(wyThur, val, later2.wy, later2.Y);
      if (later2.wy.val(year) !== 1) {
        year = later2.dw.next(year, 2);
      }
      var wyMax = later2.wy.extent(year)[1], wyStart = later2.wy.start(year);
      val = val > wyMax ? 1 : val || wyMax;
      return later2.date.next(later2.Y.val(wyStart), later2.M.val(wyStart), later2.D.val(wyStart) + 7 * (val - 1));
    },
    prev: function(d, val) {
      var wyThur = later2.dw.next(later2.wy.start(d), 5), year = later2.date.prevRollover(wyThur, val, later2.wy, later2.Y);
      if (later2.wy.val(year) !== 1) {
        year = later2.dw.next(year, 2);
      }
      var wyMax = later2.wy.extent(year)[1], wyEnd = later2.wy.end(year);
      val = val > wyMax ? wyMax : val || wyMax;
      return later2.wy.end(later2.date.next(later2.Y.val(wyEnd), later2.M.val(wyEnd), later2.D.val(wyEnd) + 7 * (val - 1)));
    }
  };
  later2.year = later2.Y = {
    name: "year",
    range: 31556900,
    val: function(d) {
      return d.Y || (d.Y = later2.date.getYear.call(d));
    },
    isValid: function(d, val) {
      return later2.Y.val(d) === val;
    },
    extent: function() {
      return [1970, 2099];
    },
    start: function(d) {
      return d.YStart || (d.YStart = later2.date.next(later2.Y.val(d)));
    },
    end: function(d) {
      return d.YEnd || (d.YEnd = later2.date.prev(later2.Y.val(d)));
    },
    next: function(d, val) {
      return val > later2.Y.val(d) && val <= later2.Y.extent()[1] ? later2.date.next(val) : later2.NEVER;
    },
    prev: function(d, val) {
      return val < later2.Y.val(d) && val >= later2.Y.extent()[0] ? later2.date.prev(val) : later2.NEVER;
    }
  };
  later2.fullDate = later2.fd = {
    name: "full date",
    range: 1,
    val: function(d) {
      return d.fd || (d.fd = d.getTime());
    },
    isValid: function(d, val) {
      return later2.fd.val(d) === val;
    },
    extent: function() {
      return [0, 3250368e7];
    },
    start: function(d) {
      return d;
    },
    end: function(d) {
      return d;
    },
    next: function(d, val) {
      return later2.fd.val(d) < val ? new Date(val) : later2.NEVER;
    },
    prev: function(d, val) {
      return later2.fd.val(d) > val ? new Date(val) : later2.NEVER;
    }
  };
  later2.modifier = {};
  later2.modifier.after = later2.modifier.a = function(constraint, values) {
    var value2 = values[0];
    return {
      name: "after " + constraint.name,
      range: (constraint.extent(/* @__PURE__ */ new Date())[1] - value2) * constraint.range,
      val: constraint.val,
      isValid: function(d, val) {
        return this.val(d) >= value2;
      },
      extent: constraint.extent,
      start: constraint.start,
      end: constraint.end,
      next: function(startDate, val) {
        if (val != value2)
          val = constraint.extent(startDate)[0];
        return constraint.next(startDate, val);
      },
      prev: function(startDate, val) {
        val = val === value2 ? constraint.extent(startDate)[1] : value2 - 1;
        return constraint.prev(startDate, val);
      }
    };
  };
  later2.modifier.before = later2.modifier.b = function(constraint, values) {
    var value2 = values[values.length - 1];
    return {
      name: "before " + constraint.name,
      range: constraint.range * (value2 - 1),
      val: constraint.val,
      isValid: function(d, val) {
        return this.val(d) < value2;
      },
      extent: constraint.extent,
      start: constraint.start,
      end: constraint.end,
      next: function(startDate, val) {
        val = val === value2 ? constraint.extent(startDate)[0] : value2;
        return constraint.next(startDate, val);
      },
      prev: function(startDate, val) {
        val = val === value2 ? value2 - 1 : constraint.extent(startDate)[1];
        return constraint.prev(startDate, val);
      }
    };
  };
  later2.compile = function(schedDef) {
    var constraints = [], constraintsLen = 0, tickConstraint;
    for (var key in schedDef) {
      var nameParts = key.split("_"), name = nameParts[0], mod = nameParts[1], vals = schedDef[key], constraint = mod ? later2.modifier[mod](later2[name], vals) : later2[name];
      constraints.push({
        constraint,
        vals
      });
      constraintsLen++;
    }
    constraints.sort(function(a, b) {
      var ra = a.constraint.range, rb = b.constraint.range;
      return rb < ra ? -1 : rb > ra ? 1 : 0;
    });
    tickConstraint = constraints[constraintsLen - 1].constraint;
    function compareFn(dir) {
      return dir === "next" ? function(a, b) {
        return a.getTime() > b.getTime();
      } : function(a, b) {
        return b.getTime() > a.getTime();
      };
    }
    return {
      start: function(dir, startDate) {
        var next = startDate, nextVal = later2.array[dir], maxAttempts = 1e3, done;
        while (maxAttempts-- && !done && next) {
          done = true;
          for (var i = 0; i < constraintsLen; i++) {
            var constraint2 = constraints[i].constraint, curVal = constraint2.val(next), extent = constraint2.extent(next), newVal = nextVal(curVal, constraints[i].vals, extent);
            if (!constraint2.isValid(next, newVal)) {
              next = constraint2[dir](next, newVal);
              done = false;
              break;
            }
          }
        }
        if (next !== later2.NEVER) {
          next = dir === "next" ? tickConstraint.start(next) : tickConstraint.end(next);
        }
        return next;
      },
      end: function(dir, startDate) {
        var result, nextVal = later2.array[dir + "Invalid"], compare = compareFn(dir);
        for (var i = constraintsLen - 1; i >= 0; i--) {
          var constraint2 = constraints[i].constraint, curVal = constraint2.val(startDate), extent = constraint2.extent(startDate), newVal = nextVal(curVal, constraints[i].vals, extent), next;
          if (newVal !== void 0) {
            next = constraint2[dir](startDate, newVal);
            if (next && (!result || compare(result, next))) {
              result = next;
            }
          }
        }
        return result;
      },
      tick: function(dir, date2) {
        return new Date(dir === "next" ? tickConstraint.end(date2).getTime() + later2.SEC : tickConstraint.start(date2).getTime() - later2.SEC);
      },
      // PATCH
      tickSafe: function(dir, date2) {
        return dir === "next" ? diffSecond(tickConstraint.end(date2), 1) : diffSecond(tickConstraint.start(date2), -1);
      },
      // EOF PATCH
      tickStart: function(date2) {
        return tickConstraint.start(date2);
      }
    };
  };
  later2.schedule = function(sched) {
    if (!sched)
      throw new Error("Missing schedule definition.");
    if (!sched.schedules)
      throw new Error("Definition must include at least one schedule.");
    var schedules = [], schedulesLen = sched.schedules.length, exceptions = [], exceptionsLen = sched.exceptions ? sched.exceptions.length : 0;
    for (var i = 0; i < schedulesLen; i++) {
      schedules.push(later2.compile(sched.schedules[i]));
    }
    for (var j = 0; j < exceptionsLen; j++) {
      exceptions.push(later2.compile(sched.exceptions[j]));
    }
    function getInstances(dir, count, startDate, endDate, isRange) {
      var compare = compareFn(dir), loopCount = count, maxAttempts = 1e6, schedStarts = [], exceptStarts = [], next, end, results = [], isForward = dir === "next", lastResult, rStart = isForward ? 0 : 1, rEnd = isForward ? 1 : 0;
      startDate = startDate ? new Date(startDate) : /* @__PURE__ */ new Date();
      if (!startDate || !startDate.getTime())
        throw new Error("Invalid start date.");
      setNextStarts(dir, schedules, schedStarts, startDate);
      setRangeStarts(dir, exceptions, exceptStarts, startDate);
      while (maxAttempts-- && loopCount && (next = findNext(schedStarts, compare))) {
        if (endDate && compare(next, endDate)) {
          break;
        }
        if (exceptionsLen) {
          updateRangeStarts(dir, exceptions, exceptStarts, next);
          if (end = calcRangeOverlap(dir, exceptStarts, next)) {
            updateNextStarts(dir, schedules, schedStarts, end);
            continue;
          }
        }
        if (isRange) {
          var maxEndDate = calcMaxEndDate(exceptStarts, compare);
          end = calcEnd(dir, schedules, schedStarts, next, maxEndDate);
          var r = isForward ? [new Date(Math.max(startDate, next)), end ? new Date(endDate ? Math.min(end, endDate) : end) : void 0] : [end ? new Date(endDate ? Math.max(endDate, end.getTime() + later2.SEC) : end.getTime() + later2.SEC) : void 0, new Date(Math.min(startDate, next.getTime() + later2.SEC))];
          if (lastResult && r[rStart].getTime() === lastResult[rEnd].getTime()) {
            lastResult[rEnd] = r[rEnd];
            loopCount++;
          } else {
            lastResult = r;
            results.push(lastResult);
          }
          if (!end)
            break;
          updateNextStarts(dir, schedules, schedStarts, end);
        } else {
          results.push(isForward ? new Date(Math.max(startDate, next)) : getStart(schedules, schedStarts, next, endDate));
          tickStarts(dir, schedules, schedStarts, next);
        }
        loopCount--;
      }
      for (var i2 = 0, len = results.length; i2 < len; i2++) {
        var result = results[i2];
        results[i2] = Object.prototype.toString.call(result) === "[object Array]" ? [cleanDate(result[0]), cleanDate(result[1])] : cleanDate(result);
      }
      return results.length === 0 ? later2.NEVER : count === 1 ? results[0] : results;
    }
    function cleanDate(d) {
      if (d instanceof Date && !isNaN(d.valueOf())) {
        return new Date(d);
      }
      return void 0;
    }
    function setNextStarts(dir, schedArr, startsArr, startDate) {
      for (var i2 = 0, len = schedArr.length; i2 < len; i2++) {
        startsArr[i2] = schedArr[i2].start(dir, startDate);
      }
    }
    function updateNextStarts(dir, schedArr, startsArr, startDate) {
      var compare = compareFn(dir);
      for (var i2 = 0, len = schedArr.length; i2 < len; i2++) {
        if (startsArr[i2] && !compare(startsArr[i2], startDate)) {
          startsArr[i2] = schedArr[i2].start(dir, startDate);
        }
      }
    }
    function setRangeStarts(dir, schedArr, rangesArr, startDate) {
      var compare = compareFn(dir);
      for (var i2 = 0, len = schedArr.length; i2 < len; i2++) {
        var nextStart = schedArr[i2].start(dir, startDate);
        if (!nextStart) {
          rangesArr[i2] = later2.NEVER;
        } else {
          rangesArr[i2] = [nextStart, schedArr[i2].end(dir, nextStart)];
        }
      }
    }
    function updateRangeStarts(dir, schedArr, rangesArr, startDate) {
      var compare = compareFn(dir);
      for (var i2 = 0, len = schedArr.length; i2 < len; i2++) {
        if (rangesArr[i2] && !compare(rangesArr[i2][0], startDate)) {
          var nextStart = schedArr[i2].start(dir, startDate);
          if (!nextStart) {
            rangesArr[i2] = later2.NEVER;
          } else {
            rangesArr[i2] = [nextStart, schedArr[i2].end(dir, nextStart)];
          }
        }
      }
    }
    function tickStarts(dir, schedArr, startsArr, startDate) {
      for (var i2 = 0, len = schedArr.length; i2 < len; i2++) {
        if (startsArr[i2] && startsArr[i2].getTime() === startDate.getTime()) {
          const newStart = schedArr[i2].start(dir, schedArr[i2].tick(dir, startDate));
          if (newStart !== later2.NEVER && newStart.getTime() === startsArr[i2].getTime()) {
            startsArr[i2] = schedArr[i2].start(dir, schedArr[i2].tickSafe(dir, startDate));
          } else {
            startsArr[i2] = newStart;
          }
        }
      }
    }
    function getStart(schedArr, startsArr, startDate, minEndDate) {
      var result;
      for (var i2 = 0, len = startsArr.length; i2 < len; i2++) {
        if (startsArr[i2] && startsArr[i2].getTime() === startDate.getTime()) {
          var start = schedArr[i2].tickStart(startDate);
          if (minEndDate && start < minEndDate) {
            return minEndDate;
          }
          if (!result || start > result) {
            result = start;
          }
        }
      }
      return result;
    }
    function calcRangeOverlap(dir, rangesArr, startDate) {
      var compare = compareFn(dir), result;
      for (var i2 = 0, len = rangesArr.length; i2 < len; i2++) {
        var range = rangesArr[i2];
        if (range && !compare(range[0], startDate) && (!range[1] || compare(range[1], startDate))) {
          if (!result || compare(range[1], result)) {
            result = range[1];
          }
        }
      }
      return result;
    }
    function calcMaxEndDate(exceptsArr, compare) {
      var result;
      for (var i2 = 0, len = exceptsArr.length; i2 < len; i2++) {
        if (exceptsArr[i2] && (!result || compare(result, exceptsArr[i2][0]))) {
          result = exceptsArr[i2][0];
        }
      }
      return result;
    }
    function calcEnd(dir, schedArr, startsArr, startDate, maxEndDate) {
      var compare = compareFn(dir), result;
      for (var i2 = 0, len = schedArr.length; i2 < len; i2++) {
        var start = startsArr[i2];
        if (start && start.getTime() === startDate.getTime()) {
          var end = schedArr[i2].end(dir, start);
          if (maxEndDate && (!end || compare(end, maxEndDate))) {
            return maxEndDate;
          }
          if (!result || compare(end, result)) {
            result = end;
          }
        }
      }
      return result;
    }
    function compareFn(dir) {
      return dir === "next" ? function(a, b) {
        return !b || a.getTime() > b.getTime();
      } : function(a, b) {
        return !a || b.getTime() > a.getTime();
      };
    }
    function findNext(arr, compare) {
      var next = arr[0];
      for (var i2 = 1, len = arr.length; i2 < len; i2++) {
        if (arr[i2] && compare(next, arr[i2])) {
          next = arr[i2];
        }
      }
      return next;
    }
    return {
      isValid: function(d) {
        return getInstances("next", 1, d, d) !== later2.NEVER;
      },
      next: function(count, startDate, endDate) {
        return getInstances("next", count || 1, startDate, endDate);
      },
      prev: function(count, startDate, endDate) {
        return getInstances("prev", count || 1, startDate, endDate);
      },
      nextRange: function(count, startDate, endDate) {
        return getInstances("next", count || 1, startDate, endDate, true);
      },
      prevRange: function(count, startDate, endDate) {
        return getInstances("prev", count || 1, startDate, endDate, true);
      }
    };
  };
  later2.setTimeout = function(fn2, sched) {
    var s = later2.schedule(sched), t;
    if (fn2) {
      scheduleTimeout();
    }
    function scheduleTimeout() {
      var now2 = Date.now(), next = s.next(2, now2);
      if (!next[0]) {
        t = void 0;
        return;
      }
      var diff = next[0].getTime() - now2;
      if (diff < 1e3) {
        diff = next[1] ? next[1].getTime() - now2 : 1e3;
      }
      if (diff < 2147483647) {
        t = setTimeout(fn2, diff);
      } else {
        t = setTimeout(scheduleTimeout, 2147483647);
      }
    }
    return {
      isDone: function() {
        return !t;
      },
      clear: function() {
        clearTimeout(t);
      }
    };
  };
  later2.setInterval = function(fn2, sched) {
    if (!fn2) {
      return;
    }
    var t = later2.setTimeout(scheduleTimeout, sched), done = t.isDone();
    function scheduleTimeout() {
      if (!done) {
        fn2();
        t = later2.setTimeout(scheduleTimeout, sched);
      }
    }
    return {
      isDone: function() {
        return t.isDone();
      },
      clear: function() {
        done = true;
        t.clear();
      }
    };
  };
  later2.date = {};
  later2.date.timezone = function(useLocalTime) {
    later2.date.build = useLocalTime ? function(Y, M, D, h, m, s) {
      return new Date(Y, M, D, h, m, s);
    } : function(Y, M, D, h, m, s) {
      return new Date(Date.UTC(Y, M, D, h, m, s));
    };
    var get = useLocalTime ? "get" : "getUTC", d = Date.prototype;
    later2.date.getYear = d[get + "FullYear"];
    later2.date.getMonth = d[get + "Month"];
    later2.date.getDate = d[get + "Date"];
    later2.date.getDay = d[get + "Day"];
    later2.date.getHour = d[get + "Hours"];
    later2.date.getMin = d[get + "Minutes"];
    later2.date.getSec = d[get + "Seconds"];
    later2.date.isUTC = !useLocalTime;
  };
  later2.date.UTC = function() {
    later2.date.timezone(false);
  };
  later2.date.localTime = function() {
    later2.date.timezone(true);
  };
  later2.date.UTC();
  later2.SEC = 1e3;
  later2.MIN = later2.SEC * 60;
  later2.HOUR = later2.MIN * 60;
  later2.DAY = later2.HOUR * 24;
  later2.WEEK = later2.DAY * 7;
  later2.DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  later2.NEVER = 0;
  later2.date.next = function(Y, M, D, h, m, s) {
    return later2.date.build(Y, M !== void 0 ? M - 1 : 0, D !== void 0 ? D : 1, h || 0, m || 0, s || 0);
  };
  later2.date.nextRollover = function(d, val, constraint, period) {
    var cur = constraint.val(d), max3 = constraint.extent(d)[1];
    return (val || max3) <= cur || val > max3 ? new Date(period.end(d).getTime() + later2.SEC) : period.start(d);
  };
  later2.date.prev = function(Y, M, D, h, m, s) {
    var len = arguments.length;
    M = len < 2 ? 11 : M - 1;
    D = len < 3 ? later2.D.extent(later2.date.next(Y, M + 1))[1] : D;
    h = len < 4 ? 23 : h;
    m = len < 5 ? 59 : m;
    s = len < 6 ? 59 : s;
    return later2.date.build(Y, M, D, h, m, s);
  };
  later2.date.prevRollover = function(d, val, constraint, period) {
    var cur = constraint.val(d);
    return val >= cur || !val ? period.start(period.prev(d, period.val(d) - 1)) : period.start(d);
  };
  later2.parse = {};
  later2.parse.cron = function(expr, hasSeconds) {
    var NAMES = {
      JAN: 1,
      FEB: 2,
      MAR: 3,
      APR: 4,
      MAY: 5,
      JUN: 6,
      JUL: 7,
      AUG: 8,
      SEP: 9,
      OCT: 10,
      NOV: 11,
      DEC: 12,
      SUN: 1,
      MON: 2,
      TUE: 3,
      WED: 4,
      THU: 5,
      FRI: 6,
      SAT: 7
    };
    var REPLACEMENTS = {
      "* * * * * *": "0/1 * * * * *",
      "@YEARLY": "0 0 1 1 *",
      "@ANNUALLY": "0 0 1 1 *",
      "@MONTHLY": "0 0 1 * *",
      "@WEEKLY": "0 0 * * 0",
      "@DAILY": "0 0 * * *",
      "@HOURLY": "0 * * * *"
    };
    var FIELDS = {
      s: [0, 0, 59],
      m: [1, 0, 59],
      h: [2, 0, 23],
      D: [3, 1, 31],
      M: [4, 1, 12],
      Y: [6, 1970, 2099],
      d: [5, 1, 7, 1]
    };
    function getValue(value2, offset, max3) {
      return isNaN(value2) ? NAMES[value2] || null : Math.min(+value2 + (offset || 0), max3 || 9999);
    }
    function cloneSchedule(sched) {
      var clone = {}, field;
      for (field in sched) {
        if (field !== "dc" && field !== "d") {
          clone[field] = sched[field].slice(0);
        }
      }
      return clone;
    }
    function add(sched, name, min4, max3, inc) {
      var i = min4;
      if (!sched[name]) {
        sched[name] = [];
      }
      while (i <= max3) {
        if (sched[name].indexOf(i) < 0) {
          sched[name].push(i);
        }
        i += inc || 1;
      }
      sched[name].sort(function(a, b) {
        return a - b;
      });
    }
    function addHash(schedules, curSched, value2, hash) {
      if (curSched.d && !curSched.dc || curSched.dc && curSched.dc.indexOf(hash) < 0) {
        schedules.push(cloneSchedule(curSched));
        curSched = schedules[schedules.length - 1];
      }
      add(curSched, "d", value2, value2);
      add(curSched, "dc", hash, hash);
    }
    function addWeekday(s, curSched, value2) {
      var except1 = {}, except2 = {};
      if (value2 === 1) {
        add(curSched, "D", 1, 3);
        add(curSched, "d", NAMES.MON, NAMES.FRI);
        add(except1, "D", 2, 2);
        add(except1, "d", NAMES.TUE, NAMES.FRI);
        add(except2, "D", 3, 3);
        add(except2, "d", NAMES.TUE, NAMES.FRI);
      } else {
        add(curSched, "D", value2 - 1, value2 + 1);
        add(curSched, "d", NAMES.MON, NAMES.FRI);
        add(except1, "D", value2 - 1, value2 - 1);
        add(except1, "d", NAMES.MON, NAMES.THU);
        add(except2, "D", value2 + 1, value2 + 1);
        add(except2, "d", NAMES.TUE, NAMES.FRI);
      }
      s.exceptions.push(except1);
      s.exceptions.push(except2);
    }
    function addRange(item, curSched, name, min4, max3, offset) {
      var incSplit = item.split("/"), inc = +incSplit[1], range = incSplit[0];
      if (range !== "*" && range !== "0") {
        var rangeSplit = range.split("-");
        min4 = getValue(rangeSplit[0], offset, max3);
        max3 = getValue(rangeSplit[1], offset, max3) || max3;
      }
      add(curSched, name, min4, max3, inc);
    }
    function parse(item, s, name, min4, max3, offset) {
      var value2, split2, schedules = s.schedules, curSched = schedules[schedules.length - 1];
      if (item === "L") {
        item = min4 - 1;
      }
      if ((value2 = getValue(item, offset, max3)) !== null) {
        add(curSched, name, value2, value2);
      } else if ((value2 = getValue(item.replace("W", ""), offset, max3)) !== null) {
        addWeekday(s, curSched, value2);
      } else if ((value2 = getValue(item.replace("L", ""), offset, max3)) !== null) {
        addHash(schedules, curSched, value2, min4 - 1);
      } else if ((split2 = item.split("#")).length === 2) {
        value2 = getValue(split2[0], offset, max3);
        addHash(schedules, curSched, value2, getValue(split2[1]));
      } else {
        addRange(item, curSched, name, min4, max3, offset);
      }
    }
    function isHash(item) {
      return item.indexOf("#") > -1 || item.indexOf("L") > 0;
    }
    function itemSorter(a, b) {
      return isHash(a) && !isHash(b) ? 1 : a - b;
    }
    function parseExpr(expr2) {
      var schedule = {
        schedules: [{}],
        exceptions: []
      }, components = expr2.replace(/(\s)+/g, " ").split(" "), field, f, component, items2;
      for (field in FIELDS) {
        f = FIELDS[field];
        component = components[f[0]];
        if (component && component !== "*" && component !== "?") {
          items2 = component.split(",").sort(itemSorter);
          var i, length = items2.length;
          for (i = 0; i < length; i++) {
            parse(items2[i], schedule, field, f[1], f[2], f[3]);
          }
        }
      }
      return schedule;
    }
    function prepareExpr(expr2) {
      var prepared = expr2.toUpperCase();
      return REPLACEMENTS[prepared] || prepared;
    }
    var e = prepareExpr(expr);
    return parseExpr(hasSeconds ? e : "0 " + e);
  };
  later2.parse.recur = function() {
    var schedules = [], exceptions = [], cur, curArr = schedules, curName, values, every2, modifier, applyMin, applyMax, i, last;
    function add(name, min4, max3) {
      name = modifier ? name + "_" + modifier : name;
      if (!cur) {
        curArr.push({});
        cur = curArr[0];
      }
      if (!cur[name]) {
        cur[name] = [];
      }
      curName = cur[name];
      if (every2) {
        values = [];
        for (i = min4; i <= max3; i += every2) {
          values.push(i);
        }
        last = {
          n: name,
          x: every2,
          c: curName.length,
          m: max3
        };
      }
      values = applyMin ? [min4] : applyMax ? [max3] : values;
      var length = values.length;
      for (i = 0; i < length; i += 1) {
        var val = values[i];
        if (curName.indexOf(val) < 0) {
          curName.push(val);
        }
      }
      values = every2 = modifier = applyMin = applyMax = 0;
    }
    return {
      schedules,
      exceptions,
      on: function() {
        values = arguments[0] instanceof Array ? arguments[0] : arguments;
        return this;
      },
      every: function(x) {
        every2 = x || 1;
        return this;
      },
      after: function(x) {
        modifier = "a";
        values = [x];
        return this;
      },
      before: function(x) {
        modifier = "b";
        values = [x];
        return this;
      },
      first: function() {
        applyMin = 1;
        return this;
      },
      last: function() {
        applyMax = 1;
        return this;
      },
      time: function() {
        for (var i2 = 0, len = values.length; i2 < len; i2++) {
          var split2 = values[i2].split(":");
          if (split2.length < 3)
            split2.push(0);
          values[i2] = +split2[0] * 3600 + +split2[1] * 60 + +split2[2];
        }
        add("t");
        return this;
      },
      second: function() {
        add("s", 0, 59);
        return this;
      },
      minute: function() {
        add("m", 0, 59);
        return this;
      },
      hour: function() {
        add("h", 0, 23);
        return this;
      },
      dayOfMonth: function() {
        add("D", 1, applyMax ? 0 : 31);
        return this;
      },
      dayOfWeek: function() {
        add("d", 1, 7);
        return this;
      },
      onWeekend: function() {
        values = [1, 7];
        return this.dayOfWeek();
      },
      onWeekday: function() {
        values = [2, 3, 4, 5, 6];
        return this.dayOfWeek();
      },
      dayOfWeekCount: function() {
        add("dc", 1, applyMax ? 0 : 5);
        return this;
      },
      dayOfYear: function() {
        add("dy", 1, applyMax ? 0 : 366);
        return this;
      },
      weekOfMonth: function() {
        add("wm", 1, applyMax ? 0 : 5);
        return this;
      },
      weekOfYear: function() {
        add("wy", 1, applyMax ? 0 : 53);
        return this;
      },
      month: function() {
        add("M", 1, 12);
        return this;
      },
      year: function() {
        add("Y", 1970, 2450);
        return this;
      },
      fullDate: function() {
        for (var i2 = 0, len = values.length; i2 < len; i2++) {
          values[i2] = values[i2].getTime();
        }
        add("fd");
        return this;
      },
      customModifier: function(id2, vals) {
        var custom = later2.modifier[id2];
        if (!custom)
          throw new Error("Custom modifier " + id2 + " not recognized!");
        modifier = id2;
        values = arguments[1] instanceof Array ? arguments[1] : [arguments[1]];
        return this;
      },
      customPeriod: function(id2) {
        var custom = later2[id2];
        if (!custom)
          throw new Error("Custom time period " + id2 + " not recognized!");
        add(id2, custom.extent(/* @__PURE__ */ new Date())[0], custom.extent(/* @__PURE__ */ new Date())[1]);
        return this;
      },
      startingOn: function(start) {
        return this.between(start, last.m);
      },
      between: function(start, end) {
        cur[last.n] = cur[last.n].splice(0, last.c);
        every2 = last.x;
        add(last.n, start, end);
        return this;
      },
      and: function() {
        cur = curArr[curArr.push({}) - 1];
        return this;
      },
      except: function() {
        curArr = exceptions;
        cur = null;
        return this;
      }
    };
  };
  later2.parse.text = function(str) {
    var recur = later2.parse.recur, pos = 0, input = "", error;
    var TOKENTYPES = {
      eof: /^$/,
      fullDate: /^(\d\d\d\d-\d\d-\d\d(t\d\d(:\d\d(:\d\d)?)?)?)\b/,
      rank: /^((\d\d\d\d)|([2-5]?1(st)?|[2-5]?2(nd)?|[2-5]?3(rd)?|(0|[1-5]?[4-9]|[1-5]0|1[1-3])(th)?))\b/,
      time: /^((([0]?[1-9]|1[0-2]):[0-5]\d(\s)?(am|pm))|(([0]?\d|1\d|2[0-3]):[0-5]\d))\b/,
      dayName: /^((sun|mon|tue(s)?|wed(nes)?|thu(r(s)?)?|fri|sat(ur)?)(day)?)\b/,
      monthName: /^(jan(uary)?|feb(ruary)?|ma((r(ch)?)?|y)|apr(il)?|ju(ly|ne)|aug(ust)?|oct(ober)?|(sept|nov|dec)(ember)?)\b/,
      yearIndex: /^(\d\d\d\d)\b/,
      every: /^every\b/,
      after: /^after\b/,
      before: /^before\b/,
      second: /^(s|sec(ond)?(s)?)\b/,
      minute: /^(m|min(ute)?(s)?)\b/,
      hour: /^(h|hour(s)?)\b/,
      day: /^(day(s)?( of the month)?)\b/,
      dayInstance: /^day instance\b/,
      dayOfWeek: /^day(s)? of the week\b/,
      dayOfYear: /^day(s)? of the year\b/,
      weekOfYear: /^week(s)?( of the year)?\b/,
      weekOfMonth: /^week(s)? of the month\b/,
      weekday: /^weekday\b/,
      weekend: /^weekend\b/,
      month: /^month(s)?\b/,
      year: /^year(s)?\b/,
      between: /^between (the)?\b/,
      start: /^(start(ing)? (at|on( the)?)?)\b/,
      at: /^(at|@)\b/,
      and: /^(,|and\b)/,
      except: /^(except\b)/,
      also: /(also)\b/,
      first: /^(first)\b/,
      last: /^last\b/,
      "in": /^in\b/,
      of: /^of\b/,
      onthe: /^on the\b/,
      on: /^on\b/,
      through: /(-|^(to|through)\b)/
    };
    var NAMES = {
      jan: 1,
      feb: 2,
      mar: 3,
      apr: 4,
      may: 5,
      jun: 6,
      jul: 7,
      aug: 8,
      sep: 9,
      oct: 10,
      nov: 11,
      dec: 12,
      sun: 1,
      mon: 2,
      tue: 3,
      wed: 4,
      thu: 5,
      fri: 6,
      sat: 7,
      "1st": 1,
      fir: 1,
      "2nd": 2,
      sec: 2,
      "3rd": 3,
      thi: 3,
      "4th": 4,
      "for": 4
    };
    function t(start, end, text, type) {
      return {
        startPos: start,
        endPos: end,
        text,
        type
      };
    }
    function peek(expected) {
      var scanTokens = expected instanceof Array ? expected : [expected], whiteSpace = /\s+/, token, curInput, m, scanToken, start, len;
      scanTokens.push(whiteSpace);
      start = pos;
      while (!token || token.type === whiteSpace) {
        len = -1;
        curInput = input.substring(start);
        token = t(start, start, input.split(whiteSpace)[0]);
        var i, length = scanTokens.length;
        for (i = 0; i < length; i++) {
          scanToken = scanTokens[i];
          m = scanToken.exec(curInput);
          if (m && m.index === 0 && m[0].length > len) {
            len = m[0].length;
            token = t(start, start + len, curInput.substring(0, len), scanToken);
          }
        }
        if (token.type === whiteSpace) {
          start = token.endPos;
        }
      }
      return token;
    }
    function scan(expectedToken) {
      var token = peek(expectedToken);
      pos = token.endPos;
      return token;
    }
    function parseThroughExpr(tokenType) {
      var start = +parseTokenValue(tokenType), end = checkAndParse(TOKENTYPES.through) ? +parseTokenValue(tokenType) : start, nums = [];
      for (var i = start; i <= end; i++) {
        nums.push(i);
      }
      return nums;
    }
    function parseRanges(tokenType) {
      var nums = parseThroughExpr(tokenType);
      while (checkAndParse(TOKENTYPES.and)) {
        nums.push.apply(nums, parseThroughExpr(tokenType));
      }
      if (tokenType === TOKENTYPES.dayName) {
        nums.sort((a, b) => a - b);
      }
      return nums;
    }
    function parseEvery(r) {
      var num, period, start, end;
      if (checkAndParse(TOKENTYPES.weekend)) {
        r.on(NAMES.sun, NAMES.sat).dayOfWeek();
      } else if (checkAndParse(TOKENTYPES.weekday)) {
        r.on(NAMES.mon, NAMES.tue, NAMES.wed, NAMES.thu, NAMES.fri).dayOfWeek();
      } else {
        num = parseTokenValue(TOKENTYPES.rank);
        r.every(num);
        period = parseTimePeriod(r);
        if (checkAndParse(TOKENTYPES.start)) {
          num = parseTokenValue(TOKENTYPES.rank);
          r.startingOn(num);
          parseToken(period.type);
        } else if (checkAndParse(TOKENTYPES.between)) {
          start = parseTokenValue(TOKENTYPES.rank);
          if (checkAndParse(TOKENTYPES.and)) {
            end = parseTokenValue(TOKENTYPES.rank);
            r.between(start, end);
          }
        }
      }
    }
    function parseOnThe(r) {
      if (checkAndParse(TOKENTYPES.first)) {
        r.first();
      } else if (checkAndParse(TOKENTYPES.last)) {
        r.last();
      } else {
        r.on(parseRanges(TOKENTYPES.rank));
      }
      parseTimePeriod(r);
    }
    function parseScheduleExpr(str2) {
      pos = 0;
      input = str2;
      error = -1;
      var r = recur();
      while (pos < input.length && error < 0) {
        var token = parseToken([TOKENTYPES.every, TOKENTYPES.after, TOKENTYPES.before, TOKENTYPES.onthe, TOKENTYPES.on, TOKENTYPES.of, TOKENTYPES["in"], TOKENTYPES.at, TOKENTYPES.and, TOKENTYPES.except, TOKENTYPES.also]);
        switch (token.type) {
          case TOKENTYPES.every:
            parseEvery(r);
            break;
          case TOKENTYPES.after:
            if (peek(TOKENTYPES.time).type !== void 0) {
              r.after(parseTokenValue(TOKENTYPES.time));
              r.time();
            } else if (peek(TOKENTYPES.fullDate).type !== void 0) {
              r.after(parseTokenValue(TOKENTYPES.fullDate));
              r.fullDate();
            } else {
              r.after(parseTokenValue(TOKENTYPES.rank));
              parseTimePeriod(r);
            }
            break;
          case TOKENTYPES.before:
            if (peek(TOKENTYPES.time).type !== void 0) {
              r.before(parseTokenValue(TOKENTYPES.time));
              r.time();
            } else if (peek(TOKENTYPES.fullDate).type !== void 0) {
              r.before(parseTokenValue(TOKENTYPES.fullDate));
              r.fullDate();
            } else {
              r.before(parseTokenValue(TOKENTYPES.rank));
              parseTimePeriod(r);
            }
            break;
          case TOKENTYPES.onthe:
            parseOnThe(r);
            break;
          case TOKENTYPES.on:
            r.on(parseRanges(TOKENTYPES.dayName)).dayOfWeek();
            break;
          case TOKENTYPES.of:
            r.on(parseRanges(TOKENTYPES.monthName)).month();
            break;
          case TOKENTYPES["in"]:
            r.on(parseRanges(TOKENTYPES.yearIndex)).year();
            break;
          case TOKENTYPES.at:
            r.on(parseTokenValue(TOKENTYPES.time)).time();
            while (checkAndParse(TOKENTYPES.and)) {
              r.on(parseTokenValue(TOKENTYPES.time)).time();
            }
            break;
          case TOKENTYPES.and:
            break;
          case TOKENTYPES.also:
            r.and();
            break;
          case TOKENTYPES.except:
            r.except();
            break;
          default:
            error = pos;
        }
      }
      return {
        schedules: r.schedules,
        exceptions: r.exceptions,
        error
      };
    }
    function parseTimePeriod(r) {
      var timePeriod = parseToken([TOKENTYPES.second, TOKENTYPES.minute, TOKENTYPES.hour, TOKENTYPES.dayOfYear, TOKENTYPES.dayOfWeek, TOKENTYPES.dayInstance, TOKENTYPES.day, TOKENTYPES.month, TOKENTYPES.year, TOKENTYPES.weekOfMonth, TOKENTYPES.weekOfYear]);
      switch (timePeriod.type) {
        case TOKENTYPES.second:
          r.second();
          break;
        case TOKENTYPES.minute:
          r.minute();
          break;
        case TOKENTYPES.hour:
          r.hour();
          break;
        case TOKENTYPES.dayOfYear:
          r.dayOfYear();
          break;
        case TOKENTYPES.dayOfWeek:
          r.dayOfWeek();
          break;
        case TOKENTYPES.dayInstance:
          r.dayOfWeekCount();
          break;
        case TOKENTYPES.day:
          r.dayOfMonth();
          break;
        case TOKENTYPES.weekOfMonth:
          r.weekOfMonth();
          break;
        case TOKENTYPES.weekOfYear:
          r.weekOfYear();
          break;
        case TOKENTYPES.month:
          r.month();
          break;
        case TOKENTYPES.year:
          r.year();
          break;
        default:
          error = pos;
      }
      return timePeriod;
    }
    function checkAndParse(tokenType) {
      var found = peek(tokenType).type === tokenType;
      if (found) {
        scan(tokenType);
      }
      return found;
    }
    function parseToken(tokenType) {
      var t2 = scan(tokenType);
      if (t2.type) {
        t2.text = convertString(t2.text, tokenType);
      } else {
        error = pos;
      }
      return t2;
    }
    function parseTokenValue(tokenType) {
      return parseToken(tokenType).text;
    }
    function convertString(str2, tokenType) {
      var output = str2;
      switch (tokenType) {
        case TOKENTYPES.time:
          var parts = str2.split(/(:|am|pm)/), hour2 = parts[3] === "pm" && parts[0] < 12 ? parseInt(parts[0], 10) + 12 : parts[0], min4 = parts[2].trim();
          output = (hour2.length === 1 ? "0" : "") + hour2 + ":" + min4;
          break;
        case TOKENTYPES.rank:
          output = parseInt(/^\d+/.exec(str2)[0], 10);
          break;
        case TOKENTYPES.monthName:
        case TOKENTYPES.dayName:
          output = NAMES[str2.substring(0, 3)];
          break;
        case TOKENTYPES.fullDate:
          output = new Date(str2.toUpperCase());
          break;
      }
      return output;
    }
    return parseScheduleExpr(str.toLowerCase());
  };
  return later2;
}();
later.date.localTime();
var later_default = later;

// ../Engine/lib/Engine/scheduling/Types.js
var TimeUnit;
(function(TimeUnit2) {
  TimeUnit2["Millisecond"] = "millisecond";
  TimeUnit2["Second"] = "second";
  TimeUnit2["Minute"] = "minute";
  TimeUnit2["ElapsedMinute"] = "eminute";
  TimeUnit2["Hour"] = "hour";
  TimeUnit2["ElapsedHour"] = "ehour";
  TimeUnit2["Day"] = "day";
  TimeUnit2["ElapsedDay"] = "eday";
  TimeUnit2["Week"] = "week";
  TimeUnit2["ElapsedWeek"] = "eweek";
  TimeUnit2["Month"] = "month";
  TimeUnit2["ElapsedMonth"] = "emonth";
  TimeUnit2["Quarter"] = "quarter";
  TimeUnit2["ElapsedQuarter"] = "equarter";
  TimeUnit2["Year"] = "year";
  TimeUnit2["ElapsedYear"] = "eyear";
})(TimeUnit || (TimeUnit = {}));
var ProjectConstraintResolution;
(function(ProjectConstraintResolution2) {
  ProjectConstraintResolution2["Honor"] = "honor";
  ProjectConstraintResolution2["Ignore"] = "ignore";
  ProjectConstraintResolution2["Conflict"] = "conflict";
})(ProjectConstraintResolution || (ProjectConstraintResolution = {}));
var ConstraintType;
(function(ConstraintType2) {
  ConstraintType2["MustStartOn"] = "muststarton";
  ConstraintType2["MustFinishOn"] = "mustfinishon";
  ConstraintType2["StartNoEarlierThan"] = "startnoearlierthan";
  ConstraintType2["StartNoLaterThan"] = "startnolaterthan";
  ConstraintType2["FinishNoEarlierThan"] = "finishnoearlierthan";
  ConstraintType2["FinishNoLaterThan"] = "finishnolaterthan";
  ConstraintType2["AsSoonAsPossible"] = "assoonaspossible";
  ConstraintType2["AsLateAsPossible"] = "aslateaspossible";
})(ConstraintType || (ConstraintType = {}));
var SchedulingMode;
(function(SchedulingMode2) {
  SchedulingMode2["Normal"] = "Normal";
  SchedulingMode2["FixedDuration"] = "FixedDuration";
  SchedulingMode2["FixedEffort"] = "FixedEffort";
  SchedulingMode2["FixedUnits"] = "FixedUnits";
})(SchedulingMode || (SchedulingMode = {}));
var DependencyValidationResult;
(function(DependencyValidationResult2) {
  DependencyValidationResult2[DependencyValidationResult2["NoError"] = 0] = "NoError";
  DependencyValidationResult2[DependencyValidationResult2["CyclicDependency"] = 1] = "CyclicDependency";
  DependencyValidationResult2[DependencyValidationResult2["DuplicatingDependency"] = 2] = "DuplicatingDependency";
})(DependencyValidationResult || (DependencyValidationResult = {}));
var DependencyType;
(function(DependencyType2) {
  DependencyType2[DependencyType2["StartToStart"] = 0] = "StartToStart";
  DependencyType2[DependencyType2["StartToEnd"] = 1] = "StartToEnd";
  DependencyType2[DependencyType2["EndToStart"] = 2] = "EndToStart";
  DependencyType2[DependencyType2["EndToEnd"] = 3] = "EndToEnd";
})(DependencyType || (DependencyType = {}));
var DependenciesCalendar;
(function(DependenciesCalendar2) {
  DependenciesCalendar2["Project"] = "Project";
  DependenciesCalendar2["FromEvent"] = "FromEvent";
  DependenciesCalendar2["ToEvent"] = "ToEvent";
  DependenciesCalendar2["AllWorking"] = "AllWorking";
})(DependenciesCalendar || (DependenciesCalendar = {}));
var StartedTaskScheduling;
(function(StartedTaskScheduling2) {
  StartedTaskScheduling2["Auto"] = "Auto";
  StartedTaskScheduling2["Manual"] = "Manual";
})(StartedTaskScheduling || (StartedTaskScheduling = {}));
var ProjectType;
(function(ProjectType2) {
  ProjectType2[ProjectType2["SchedulerBasic"] = 1] = "SchedulerBasic";
  ProjectType2[ProjectType2["SchedulerPro"] = 2] = "SchedulerPro";
  ProjectType2[ProjectType2["Gantt"] = 3] = "Gantt";
})(ProjectType || (ProjectType = {}));
var Direction;
(function(Direction2) {
  Direction2["Forward"] = "Forward";
  Direction2["Backward"] = "Backward";
  Direction2["None"] = "None";
})(Direction || (Direction = {}));
var isEqualEffectiveDirection = (a, b) => {
  if (a && !b || !a && b)
    return false;
  if (!a && !b)
    return true;
  return a.direction === b.direction && (a.kind === "own" && b.kind === "own" || a.kind === "enforced" && b.kind === "enforced" && a.enforcedBy === b.enforcedBy || a.kind === "inherited" && b.kind === "inherited" && a.inheritedFrom === b.inheritedFrom);
};
var ConstraintIntervalSide;
(function(ConstraintIntervalSide2) {
  ConstraintIntervalSide2["Start"] = "Start";
  ConstraintIntervalSide2["End"] = "End";
})(ConstraintIntervalSide || (ConstraintIntervalSide = {}));
var CalendarIntervalType;
(function(CalendarIntervalType2) {
  CalendarIntervalType2["Week"] = "Week";
  CalendarIntervalType2["Exception"] = "Exception";
})(CalendarIntervalType || (CalendarIntervalType = {}));
var TreatInconsistentIntervals;
(function(TreatInconsistentIntervals2) {
  TreatInconsistentIntervals2["Skip"] = "Skip";
  TreatInconsistentIntervals2["Throw"] = "Throw";
})(TreatInconsistentIntervals || (TreatInconsistentIntervals = {}));

// ../Engine/lib/Engine/calendar/CalendarIntervalMixin.js
function convertAvailabilityValue(value2) {
  if (value2) {
    if (typeof value2 == "string") {
      value2 = value2.split(",");
      value2.map((range) => {
        const [startDate, endDate] = range.split("-");
        return { startDate, endDate };
      });
    }
  } else {
    value2 = null;
  }
  return value2;
}
var CalendarIntervalMixin = class extends Mixin([AbstractPartOfProjectModelMixin], (base) => {
  class CalendarIntervalMixin2 extends base {
    static get fields() {
      return [
        "name",
        { name: "startDate", type: "date" },
        { name: "endDate", type: "date" },
        "recurrentStartDate",
        "recurrentEndDate",
        "cls",
        "color",
        "iconCls",
        { name: "isWorking", type: "boolean", defaultValue: false },
        { name: "priority", type: "number" },
        { name: "type" },
        { name: "availability", convert: convertAvailabilityValue },
        { name: "compositeCode" },
        "recurrentTimeZone"
      ];
    }
    getCalendar() {
      return this.stores[0].calendar;
    }
    get compositeIntervalCode() {
      var _a5, _b2;
      return this.compositeCode || (((_a5 = this.startDate) == null ? void 0 : _a5.getTime()) || "") + "-" + (((_b2 = this.endDate) == null ? void 0 : _b2.getTime()) || "") + "-" + (this.type || "");
    }
    resetPriority() {
      this.prioritySortValue = null;
      this.getCalendar().getDepth();
    }
    getPrioritySortValue() {
      if (this.prioritySortValue != null)
        return this.prioritySortValue;
      let base2 = 1e4;
      let priority = this.priority;
      if (priority == null) {
        switch (this.type) {
          case CalendarIntervalType.Week:
            base2 += this.getCalendar().getDepth() * 100;
            priority = this.isOverride() ? 30 : 20;
            break;
          case CalendarIntervalType.Exception:
            base2 += this.getCalendar().getDepth() * 1e4;
            priority = 40;
            break;
          default:
            base2 += this.getCalendar().getDepth() * 100;
            if (this.isRecurrent()) {
              priority = 20;
              if (this.isOverride()) {
                priority += 10;
              }
            } else {
              priority = 40;
            }
        }
        priority += this.parentIndex;
      }
      return this.prioritySortValue = base2 + priority;
    }
    /**
     * Whether this interval is recurrent (both [[recurrentStartDate]] and [[recurrentEndDate]] are present and parsed correctly
     * by the `later` library)
     */
    isRecurrent() {
      return Boolean(this.getStartDateSchedule() || // exceptions w/ daily availability are recurrent by definition
      this.type === CalendarIntervalType.Exception && this.getAvailabilityStartSchedule());
    }
    /**
     * Whether this interval is static - both [[startDate]] and [[endDate]] are present and no recurrence rules provided to
     * [[recurrentStartDate]] and [[recurrentEndDate]] fields.
     */
    isStatic() {
      return Boolean(this.startDate && this.endDate && !this.isRecurrent());
    }
    isOverride() {
      return Boolean(this.startDate || this.endDate);
    }
    /**
     * Helper method to parse [[recurrentStartDate]] and [[recurrentEndDate]] field values.
     * @param {Object|String} schedule Recurrence schedule
     * @returns {Object} Processed schedule ready to be used by later.schedule() method.
     * @private
     */
    parseDateSchedule(value2) {
      let schedule = value2;
      if (value2 && value2 !== Object(value2)) {
        try {
          schedule = JSON.parse(value2);
        } catch (e) {
          schedule = later_default.parse.text(value2.trim());
          if (schedule.error >= 0) {
            throw new Error(`Parsing the 'later' schedule has failed: "${value2}". Please consult the 'later' documentation for recognizable formats: https://bunkat.github.io/later/parsers.html`);
          }
        }
      }
      return schedule;
    }
    getStartDateSchedule() {
      var _a5;
      if (!this.recurrentStartDate)
        return null;
      if (this.startDateSchedule)
        return this.startDateSchedule;
      const schedule = this.parseDateSchedule(this.recurrentStartDate);
      if ((_a5 = schedule == null ? void 0 : schedule.schedules) == null ? void 0 : _a5[0]) {
        const sched = schedule.schedules[0];
        if (!this.startDate && sched.fd_a) {
          this.startDate = new Date(sched.fd_a[0]);
          delete sched.fd_a;
        }
        if (!this.endDate && sched.fd_b) {
          this.endDate = new Date(sched.fd_b[0]);
          delete sched.fd_b;
        }
      }
      return this.startDateSchedule = later_default.schedule(schedule);
    }
    getEndDateSchedule() {
      var _a5, _b2, _c2, _d, _e, _f, _g, _h, _i;
      if (this.endDateSchedule)
        return this.endDateSchedule;
      if (!this.recurrentEndDate || this.recurrentEndDate === "EOD")
        return null;
      const schedule = this.parseDateSchedule(this.recurrentEndDate);
      if ((_a5 = schedule == null ? void 0 : schedule.schedules) == null ? void 0 : _a5[0]) {
        const sched = schedule.schedules[0];
        if (((_b2 = this.startDate) == null ? void 0 : _b2.getTime()) && ((_c2 = sched.fd_a) == null ? void 0 : _c2[0]) && ((_d = this.startDate) == null ? void 0 : _d.getTime()) === ((_e = sched.fd_a) == null ? void 0 : _e[0])) {
          delete sched.fd_a;
        }
        if (((_f = this.endDate) == null ? void 0 : _f.getTime()) && ((_g = sched.fd_b) == null ? void 0 : _g[0]) && ((_h = this.endDate) == null ? void 0 : _h.getTime()) === ((_i = sched.fd_b) == null ? void 0 : _i[0])) {
          delete sched.fd_b;
        }
      }
      return this.endDateSchedule = later_default.schedule(schedule);
    }
    getAvailabilityStartSchedule() {
      var _a5;
      if ((_a5 = this.availability) == null ? void 0 : _a5.length) {
        if (this.availabilityStartSchedule)
          return this.availabilityStartSchedule;
        const textRule = "at " + this.availability.map((range) => range.startDate).sort().join(",");
        const schedule = this.parseDateSchedule(textRule);
        return this.availabilityStartSchedule = later_default.schedule(schedule);
      }
    }
    getAvailabilityEndSchedule() {
      var _a5;
      if ((_a5 = this.availability) == null ? void 0 : _a5.length) {
        if (this.availabilityEndSchedule)
          return this.availabilityEndSchedule;
        const textRule = "at " + this.availability.map((range) => range.endDate).sort().join(",");
        const schedule = this.parseDateSchedule(textRule);
        return this.availabilityEndSchedule = later_default.schedule(schedule);
      }
    }
    afterChange(_toSet, wasSet, _silent, _fromRelationUpdate, _skipAccessors) {
      super.afterChange(_toSet, wasSet, _silent, _fromRelationUpdate, _skipAccessors);
      if (wasSet == null ? void 0 : wasSet.recurrentStartDate) {
        this.startDateSchedule = void 0;
      }
      if (wasSet == null ? void 0 : wasSet.recurrentEndDate) {
        this.endDateSchedule = void 0;
      }
      if (wasSet == null ? void 0 : wasSet.availability) {
        this.availabilityStartSchedule = void 0;
        this.availabilityEndSchedule = void 0;
      }
    }
  }
  return CalendarIntervalMixin2;
}) {
};

// ../Engine/lib/Engine/calendar/CalendarIntervalStore.js
var CalendarIntervalStore = class extends Mixin([AbstractPartOfProjectStoreMixin], (base) => {
  const superProto2 = base.prototype;
  class CalendarIntervalStore2 extends base {
    constructor() {
      super(...arguments);
      this.disableHasLoadedDataToCommitFlag = true;
    }
    static get defaultConfig() {
      return {
        modelClass: CalendarIntervalMixin
      };
    }
    acceptChanges() {
      if (!this.isSyncingDataOnLoad) {
        return super.acceptChanges(...arguments);
      }
    }
  }
  return CalendarIntervalStore2;
}) {
};

// ../Engine/lib/Engine/util/Constants.js
var MIN_DATE2 = /* @__PURE__ */ new Date(-864e13);
var MAX_DATE2 = /* @__PURE__ */ new Date(864e13);
var isDateFinite = (date2) => {
  if (!date2)
    return false;
  const time = date2.getTime();
  return time !== MIN_DATE2.getTime() && time !== MAX_DATE2.getTime();
};

// ../Engine/lib/Engine/util/Types.js
var EdgeInclusion;
(function(EdgeInclusion2) {
  EdgeInclusion2[EdgeInclusion2["Left"] = 0] = "Left";
  EdgeInclusion2[EdgeInclusion2["Right"] = 1] = "Right";
})(EdgeInclusion || (EdgeInclusion = {}));

// ../Engine/lib/Engine/calendar/CalendarCache.js
var CalendarIteratorResult;
(function(CalendarIteratorResult2) {
  CalendarIteratorResult2[CalendarIteratorResult2["FullRangeIterated"] = 0] = "FullRangeIterated";
  CalendarIteratorResult2[CalendarIteratorResult2["StoppedByIterator"] = 1] = "StoppedByIterator";
  CalendarIteratorResult2[CalendarIteratorResult2["MaxCacheExtendCyclesReached"] = 2] = "MaxCacheExtendCyclesReached";
  CalendarIteratorResult2[CalendarIteratorResult2["MaxRangeReached"] = 3] = "MaxRangeReached";
})(CalendarIteratorResult || (CalendarIteratorResult = {}));
var CalendarCache = class {
  constructor(config) {
    this.cacheFilledStartDate = MAX_DATE2;
    this.cacheFilledEndDate = MIN_DATE2;
    this.intervalsCachingChunkDuration = 30;
    this.intervalsCachingChunkUnit = TimeUnit.Day;
    this.maxCacheExtendCycles = 1e3;
    this.maxRange = 5 * 365 * 24 * 60 * 60 * 1e3;
    config && Object.assign(this, config);
  }
  includeWrappingRangeFrom(cache, startDate, endDate) {
    cache.ensureCacheFilledForInterval(startDate, endDate);
    this.intervalCache.includeWrappingRange(cache.intervalCache, startDate, endDate);
  }
  // after this method, we guarantee, that for every point between `startDate` and `endDate` (_inclusive_)
  // we'll have a final representation of the cache, that is, we'll be able to get an interval to which this point belongs
  // _both_ for forward and backward directions
  ensureCacheFilledForInterval(startDate, endDate) {
    const cacheFilledStartDateN = this.cacheFilledStartDate.getTime();
    const cacheFilledEndDateN = this.cacheFilledEndDate.getTime();
    if (cacheFilledStartDateN !== MAX_DATE2.getTime()) {
      const startDateN = startDate.getTime();
      const endDateN = endDate.getTime();
      if (cacheFilledStartDateN <= startDateN && endDateN <= cacheFilledEndDateN)
        return;
      if (endDateN <= cacheFilledStartDateN) {
        endDate = new Date(cacheFilledStartDateN - 1);
      } else if (startDateN >= cacheFilledEndDateN) {
        startDate = new Date(cacheFilledEndDateN);
      } else if (cacheFilledStartDateN <= startDateN && startDateN <= cacheFilledEndDateN) {
        startDate = new Date(cacheFilledEndDateN + 1);
      } else if (cacheFilledStartDateN <= endDateN && endDateN <= cacheFilledEndDateN) {
        endDate = new Date(cacheFilledStartDateN - 1);
      } else {
        this.ensureCacheFilledForInterval(startDate, new Date(cacheFilledStartDateN - 1));
        this.ensureCacheFilledForInterval(new Date(cacheFilledEndDateN + 1), endDate);
        return;
      }
    }
    if (cacheFilledStartDateN === MAX_DATE2.getTime() || startDate.getTime() < cacheFilledEndDateN) {
      this.cacheFilledStartDate = startDate;
    }
    if (cacheFilledEndDateN === MIN_DATE2.getTime() || cacheFilledEndDateN < endDate.getTime()) {
      this.cacheFilledEndDate = endDate;
    }
    this.fillCache(startDate, endDate);
  }
  fillCache(_1, _2) {
    throw new Error("Abstract method");
  }
  clear() {
    this.cacheFilledStartDate = MAX_DATE2;
    this.cacheFilledEndDate = MIN_DATE2;
    this.intervalCache.clear();
  }
  /**
   * The core iterator method of the calendar cache.
   *
   * @param options The options for iterator. Should contain at least one of the `startDate`/`endDate` properties
   * which indicates what timespan to examine for availability intervals. If one of boundaries is not provided
   * iterator function should return `false` at some point, to avoid infinite loops.
   *
   * Another recognized option is `isForward`, which indicates the direction in which to iterate through the timespan.
   *
   * Another recognized option is `maxRange`, which indicates the maximum timespan for this iterator (in milliseconds). When iterator
   * exceeds this timespan, the iteration is stopped and [[CalendarIteratorResult.MaxRangeReached]] value is returned.
   * Default value is 5 years.
   *
   * @param func The iterator function to call. It will be called for every distinct set of availability intervals, found
   * in the given timespan. All the intervals, which are "active" for current interval are collected in the 3rd argument
   * for this function. If iterator returns `false` (checked with `===`) the iteration stops.
   *
   * @param scope The scope (`this` value) to execute the iterator in.
   */
  forEachAvailabilityInterval(options, func, scope) {
    var _a5;
    scope = scope || this;
    const startDate = options.startDate;
    const endDate = options.endDate;
    const startDateN = startDate && startDate.getTime();
    const endDateN = endDate && endDate.getTime();
    const maxRange = (_a5 = options.maxRange) != null ? _a5 : this.maxRange;
    const isForward = options.isForward !== false;
    if (isForward ? !startDate : !endDate) {
      throw new Error("At least `startDate` or `endDate` is required, depending from the `isForward` option");
    }
    const intervalCache = this.intervalCache;
    let cacheCursorDate = isForward ? startDate : endDate;
    let cursorDate = isForward ? startDate : endDate;
    const rangeStart = cursorDate.getTime();
    for (let cycle = 1; cycle < this.maxCacheExtendCycles; cycle++) {
      if (isForward) {
        this.ensureCacheFilledForInterval(cacheCursorDate, endDate || DateHelper.add(cacheCursorDate, this.intervalsCachingChunkDuration, this.intervalsCachingChunkUnit));
      } else {
        this.ensureCacheFilledForInterval(startDate || DateHelper.add(cacheCursorDate, -this.intervalsCachingChunkDuration, this.intervalsCachingChunkUnit), cacheCursorDate);
      }
      let interval = intervalCache.getIntervalOf(cursorDate, isForward ? EdgeInclusion.Left : EdgeInclusion.Right);
      while (interval) {
        const intervalStartDate = interval.startDate;
        const intervalEndDate = interval.endDate;
        if (isForward && endDateN && intervalStartDate.getTime() >= endDateN || !isForward && startDateN && intervalEndDate.getTime() <= startDateN) {
          return CalendarIteratorResult.FullRangeIterated;
        }
        if (isForward && intervalStartDate.getTime() - rangeStart >= maxRange || !isForward && rangeStart - intervalEndDate.getTime() >= maxRange) {
          return CalendarIteratorResult.MaxRangeReached;
        }
        if (isForward && intervalStartDate.getTime() >= this.cacheFilledEndDate.getTime() || !isForward && intervalEndDate.getTime() <= this.cacheFilledStartDate.getTime()) {
          break;
        }
        cursorDate = isForward ? intervalEndDate : intervalStartDate;
        const countFrom = startDateN && intervalStartDate.getTime() < startDateN ? startDate : intervalStartDate;
        const countTill = endDateN && intervalEndDate.getTime() > endDateN ? endDate : intervalEndDate;
        if (func.call(scope, countFrom, countTill, interval.cacheInterval) === false) {
          return CalendarIteratorResult.StoppedByIterator;
        }
        interval = isForward ? intervalCache.getNextInterval(interval) : intervalCache.getPrevInterval(interval);
      }
      if (isForward && cursorDate.getTime() === MAX_DATE2.getTime() || !isForward && cursorDate.getTime() === MIN_DATE2.getTime()) {
        return CalendarIteratorResult.FullRangeIterated;
      }
      cacheCursorDate = isForward ? this.cacheFilledEndDate : this.cacheFilledStartDate;
    }
    return CalendarIteratorResult.MaxCacheExtendCyclesReached;
  }
};

// ../Engine/lib/Engine/util/StripDuplicates.js
var stripDuplicates = (array) => Array.from(new Set(array));

// ../Engine/lib/Engine/calendar/CalendarCacheInterval.js
var IntervalSorterFn = (interval1, interval2) => {
  const priority1 = interval1.getPrioritySortValue();
  const priority2 = interval2.getPrioritySortValue();
  return priority2 - priority1;
};
var CalendarCacheInterval = class _CalendarCacheInterval {
  constructor(config) {
    this.intervals = [];
    config && Object.assign(this, config);
    this.intervals = this.normalizeIntervals(this.intervals);
    if (!this.calendar)
      throw new Error("Required attribute `calendar` is missing");
  }
  includeInterval(interval) {
    if (this.intervals.indexOf(interval) == -1) {
      const copy = this.intervals.slice();
      copy.push(interval);
      return new _CalendarCacheInterval({ intervals: copy, calendar: this.calendar });
    } else
      return this;
  }
  combineWith(interval) {
    return new _CalendarCacheInterval({ intervals: this.intervals.concat(interval.intervals), calendar: this.calendar });
  }
  /**
   * Returns the working status of this intervals set. It is determined as a working status
   * of the most prioritized interval (intervals are prioritized from child to parent)
   */
  getIsWorking() {
    if (this.isWorking != null)
      return this.isWorking;
    return this.isWorking = this.intervals[0].isWorking;
  }
  normalizeIntervals(intervals) {
    const filtered = stripDuplicates(intervals);
    filtered.sort(IntervalSorterFn);
    return filtered;
  }
};

// ../Engine/lib/Engine/util/BinarySearch.js
var binarySearch = (value2, array, comparator = (a, b) => a - b) => {
  let left = 0;
  let right = array.length;
  while (left < right) {
    const mid = (left + right) / 2 | 0;
    const compare = comparator(value2, array[mid]);
    if (compare === 0)
      return { found: true, index: mid };
    else if (compare < 0)
      right = mid;
    else
      left = mid + 1;
  }
  return { found: false, index: right };
};

// ../Engine/lib/Engine/calendar/SortedMap.js
var IndexPosition;
(function(IndexPosition2) {
  IndexPosition2[IndexPosition2["Exact"] = 0] = "Exact";
  IndexPosition2[IndexPosition2["Next"] = 1] = "Next";
})(IndexPosition || (IndexPosition = {}));
var SortedMap = class {
  constructor(comparator) {
    this.keys = [];
    this.values = [];
    this.comparator = comparator || ((a, b) => a - b);
  }
  set(key, value2) {
    const search = binarySearch(key, this.keys, this.comparator);
    if (search.found) {
      this.values[search.index] = value2;
    } else {
      this.keys.splice(search.index, 0, key);
      this.values.splice(search.index, 0, value2);
    }
    return search.index;
  }
  // you need to know what you are doing when using this method
  insertAt(index, key, value2) {
    this.keys.splice(index, 0, key);
    this.values.splice(index, 0, value2);
  }
  setValueAt(index, value2) {
    this.values[index] = value2;
  }
  get(key) {
    const search = binarySearch(key, this.keys, this.comparator);
    return search.found ? this.values[search.index] : void 0;
  }
  getEntryAt(index) {
    return index < this.keys.length ? { key: this.keys[index], value: this.values[index] } : void 0;
  }
  getKeyAt(index) {
    return this.keys[index];
  }
  getValueAt(index) {
    return this.values[index];
  }
  delete(key) {
    const search = binarySearch(key, this.keys, this.comparator);
    if (search.found)
      this.deleteAt(search.index);
  }
  size() {
    return this.keys.length;
  }
  deleteAt(index) {
    this.keys.splice(index, 1);
    this.values.splice(index, 1);
  }
  indexOfKey(key) {
    const search = binarySearch(key, this.keys, this.comparator);
    return {
      found: search.found ? IndexPosition.Exact : IndexPosition.Next,
      index: search.index
    };
  }
  map(func) {
    const keys = this.keys;
    const values = this.values;
    const result = [];
    for (let i = 0; i < keys.length; i++)
      result.push(func(values[i], keys[i], i));
    return result;
  }
  getAllEntries() {
    return this.map((value2, key) => {
      return { value: value2, key };
    });
  }
  clear() {
    this.keys.length = 0;
    this.values.length = 0;
  }
};

// ../Engine/lib/Engine/calendar/IntervalCache.js
var IntervalCache = class {
  constructor(config) {
    this.points = new SortedMap((a, b) => a.getTime() - b.getTime());
    this.leftInfinityKey = MIN_DATE2;
    this.rightInfinityKey = MAX_DATE2;
    Object.assign(this, config);
    if (this.emptyInterval === void 0 || !this.combineIntervalsFn)
      throw new Error("All of `emptyPoint`, `combineIntervalsFn` are required");
    this.points.set(this.leftInfinityKey, this.emptyInterval);
  }
  size() {
    return this.points.size();
  }
  indexOf(date2) {
    return this.points.indexOfKey(date2);
  }
  getDateAt(index) {
    return this.points.getKeyAt(index);
  }
  getPointAt(index) {
    return this.points.getValueAt(index);
  }
  getIntervalOf(date2, edgeInclusion = EdgeInclusion.Left) {
    let { found, index } = this.indexOf(date2);
    let startDateIndex;
    if (edgeInclusion === EdgeInclusion.Left) {
      startDateIndex = found === IndexPosition.Exact ? index : index - 1;
    } else {
      startDateIndex = index - 1;
    }
    return this.getIntervalWithStartDateIndex(startDateIndex);
  }
  getPrevInterval(interval) {
    if (interval.startDateIndex === 0)
      return null;
    return this.getIntervalWithStartDateIndex(interval.startDateIndex - 1);
  }
  getNextInterval(interval) {
    if (interval.startDateIndex >= this.size() - 1)
      return null;
    return this.getIntervalWithStartDateIndex(interval.startDateIndex + 1);
  }
  getIntervalWithStartDateIndex(startDateIndex) {
    return {
      startDateIndex,
      startDate: this.getDateAt(startDateIndex),
      endDate: startDateIndex + 1 < this.size() ? this.getDateAt(startDateIndex + 1) : this.rightInfinityKey,
      cacheInterval: this.getPointAt(startDateIndex)
    };
  }
  addInterval(startDate, endDate, extendInterval) {
    const points = this.points;
    const { found, index } = points.indexOfKey(startDate);
    let curIndex;
    let lastUpdatedPoint;
    if (found == IndexPosition.Exact) {
      const inclusion = extendInterval(lastUpdatedPoint = points.getValueAt(index));
      points.setValueAt(index, inclusion);
      curIndex = index + 1;
    } else {
      const inclusion = extendInterval(lastUpdatedPoint = points.getValueAt(index - 1));
      points.insertAt(index, startDate, inclusion);
      curIndex = index + 1;
    }
    while (curIndex < points.size()) {
      const curDate = points.getKeyAt(curIndex);
      if (curDate.getTime() >= endDate.getTime())
        break;
      const inclusion = extendInterval(lastUpdatedPoint = points.getValueAt(curIndex));
      points.setValueAt(curIndex, inclusion);
      curIndex++;
    }
    if (curIndex === points.size()) {
      points.insertAt(points.size(), endDate, this.emptyInterval);
    } else {
      const curDate = points.getKeyAt(curIndex);
      if (curDate.getTime() === endDate.getTime()) {
      } else {
        points.insertAt(curIndex, endDate, lastUpdatedPoint);
      }
    }
  }
  includeWrappingRange(intervalCache, startDate, endDate) {
    let interval = intervalCache.getIntervalOf(startDate);
    while (interval) {
      this.addInterval(interval.startDate, interval.endDate, (existingInterval) => this.combineIntervalsFn(existingInterval, interval.cacheInterval));
      if (interval.endDate.getTime() > endDate.getTime())
        break;
      interval = intervalCache.getNextInterval(interval);
    }
  }
  getSummary() {
    return this.points.map((label, date2) => {
      return { label, date: date2 };
    });
  }
  clear() {
    this.points.clear();
    this.points.set(this.leftInfinityKey, this.emptyInterval);
  }
};

// ../Engine/lib/Engine/calendar/CalendarCacheSingle.js
var everySingleDaySchedule = later_default.schedule(later_default.parse.text("every 1 day"));
var CalendarCacheSingle = class extends CalendarCache {
  constructor(config) {
    super(config);
    this.staticIntervalsCached = false;
    if (!this.unspecifiedTimeInterval)
      throw new Error("Required attribute `unspecifiedTimeInterval` is missing");
    this.intervalUnspecifiedTimeIntervals = /* @__PURE__ */ new Map();
    this.weekUnspecifiedTimeIntervalsByKey = /* @__PURE__ */ new Map();
    this.treatInconsistentIntervals = (config == null ? void 0 : config.treatInconsistentIntervals) || TreatInconsistentIntervals.Skip;
    this.intervalCache = new IntervalCache({
      emptyInterval: new CalendarCacheInterval({
        intervals: [this.unspecifiedTimeInterval],
        calendar: this.calendar
      }),
      combineIntervalsFn: (interval1, interval2) => {
        return interval1.combineWith(interval2);
      }
    });
  }
  fillCache(startDate, endDate) {
    var _a5;
    if (!this.staticIntervalsCached) {
      this.cacheStaticIntervals();
      this.staticIntervalsCached = true;
    }
    if (this.parentCache)
      this.includeWrappingRangeFrom(this.parentCache, startDate, endDate);
    const timeZone = this.calendar.ignoreTimeZone ? null : (_a5 = this.calendar.project) == null ? void 0 : _a5.timeZone;
    if (startDate > endDate)
      throw new Error("Invalid cache fill interval");
    for (const interval of this.intervalStore) {
      if (interval.isRecurrent()) {
        this.cacheRecurrentInterval(startDate, endDate, timeZone, interval);
      }
    }
  }
  cacheRecurrentInterval(startDate, endDate, timeZone, interval) {
    const intervalStartDate = interval.startDate;
    const intervalEndDate = interval.endDate;
    const recurrentDatesTimeZone = interval.recurrentDatesTimeZone;
    if (intervalStartDate && intervalStartDate >= endDate || intervalEndDate && intervalEndDate <= startDate)
      return;
    const availabilityStartSchedule = interval.getAvailabilityStartSchedule();
    const availabilityEndSchedule = interval.getAvailabilityEndSchedule();
    const startSchedule = interval.getStartDateSchedule() || availabilityStartSchedule && everySingleDaySchedule;
    const endSchedule = interval.getEndDateSchedule();
    const useEndOfDay = !endSchedule || endSchedule === "EOD";
    let wrappingStartDate = startDate;
    let wrappingEndDate = endDate;
    let starts = startSchedule.prev(2, startDate);
    let ends;
    if (starts) {
      wrappingStartDate = starts[0];
      if (wrappingStartDate.getTime() === startDate.getTime() && starts[1]) {
        wrappingStartDate = starts[1];
      }
    }
    if (useEndOfDay) {
      const tmpDate = startSchedule.next(1, endDate);
      if (tmpDate) {
        wrappingEndDate = DateHelper.getStartOfNextDay(tmpDate, true);
      }
    } else {
      ends = endSchedule.next(2, endDate);
      if (ends) {
        wrappingEndDate = ends[0];
        if (wrappingEndDate.getTime() === endDate.getTime() && ends[1]) {
          wrappingEndDate = ends[1];
        }
      }
    }
    let startDates = startSchedule.next(Infinity, wrappingStartDate, new Date(wrappingEndDate.getTime() - 1e3));
    if (!(startDates == null ? void 0 : startDates.length))
      return;
    let endDates = useEndOfDay ? startDates.map((date2) => DateHelper.getStartOfNextDay(date2, true)) : endSchedule.next(Infinity, wrappingStartDate, wrappingEndDate);
    if (!(endDates == null ? void 0 : endDates.length))
      return;
    if (startDates[0].getTime() === endDates[0].getTime()) {
      endDates.splice(0, 1);
    }
    while (intervalStartDate && startDates[0] < intervalStartDate) {
      if (endDates[0] > intervalStartDate) {
        startDates[0] = intervalStartDate;
      } else {
        startDates.shift();
        endDates.shift();
      }
    }
    while (intervalEndDate && endDates[endDates.length - 1] > intervalEndDate) {
      if (startDates[endDates.length - 1] < intervalEndDate) {
        endDates[endDates.length - 1] = intervalEndDate;
      } else {
        startDates.pop();
        endDates.pop();
      }
    }
    const treatInconsistentIntervals = this.treatInconsistentIntervals;
    for (let index = 0, length = startDates.length; index < length; index++) {
      const startDate2 = startDates[index], endDate2 = endDates[index];
      if (!startDate2 || !endDate2) {
        break;
      }
      if (startDate2 > endDate2) {
        if (treatInconsistentIntervals === TreatInconsistentIntervals.Skip) {
          endDates.splice(index, 1);
        } else if (treatInconsistentIntervals === TreatInconsistentIntervals.Throw) {
          throw new Error("Recurrent interval inconsistency: " + (interval.name || interval.id) + ", caching startDate: " + DateHelper.format(startDate2) + ", caching endDate: " + DateHelper.format(endDate2) + ", recurrentStartDate: " + interval.recurrentStartDate + ", recurrentEndDate: " + interval.recurrentEndDate);
        }
      }
    }
    if (endDates.length > startDates.length) {
      endDates.length = startDates.length;
    } else if (endDates.length < startDates.length) {
      startDates.length = endDates.length;
    }
    if (availabilityStartSchedule) {
      const tmpStartDates = [], tmpEndDates = [], baseInterval = this.getUnspecifiedTimeIntervalForException(interval, { isWorking: false });
      for (let index = 0, length = startDates.length; index < length; index++) {
        const startDate2 = startDates[index], endDate2 = endDates[index];
        this.intervalCache.addInterval(startDate2, endDate2, (existingCacheInterval) => existingCacheInterval.includeInterval(baseInterval));
        let starts2 = availabilityStartSchedule.next(Infinity, startDate2, endDate2);
        if (!starts2)
          return;
        let ends2 = availabilityEndSchedule.next(Infinity, new Date(startDate2.getTime() + 1e3), endDate2);
        if (!ends2)
          return;
        if (ends2.length > starts2.length) {
          ends2.length = starts2.length;
        } else if (ends2.length < starts2.length) {
          starts2.length = ends2.length;
        }
        if (ends2[0] < starts2[0]) {
          starts2.unshift(startDate2);
          ends2.push(endDate2);
        }
        tmpStartDates.push(...starts2);
        tmpEndDates.push(...ends2);
      }
      startDates = tmpStartDates;
      endDates = tmpEndDates;
    }
    for (let index = 0, length = startDates.length; index < length; index++) {
      let recStartDate = startDates[index];
      let recEndDate = endDates[index];
      if (recurrentDatesTimeZone != null) {
        recStartDate = TimeZoneHelper.fromTimeZone(recStartDate, recurrentDatesTimeZone);
        recEndDate = TimeZoneHelper.fromTimeZone(recEndDate, recurrentDatesTimeZone);
      }
      if (timeZone != null) {
        recStartDate = TimeZoneHelper.toTimeZone(recStartDate, timeZone);
        recEndDate = TimeZoneHelper.toTimeZone(recEndDate, timeZone);
      }
      this.intervalCache.addInterval(recStartDate, recEndDate, (existingCacheInterval) => existingCacheInterval.includeInterval(interval));
    }
  }
  getUnspecifiedTimeIntervalForException(interval, data = {}) {
    let result = this.intervalUnspecifiedTimeIntervals.get(interval);
    if (!result) {
      result = this.unspecifiedTimeInterval.copy();
      this.intervalUnspecifiedTimeIntervals.set(interval, result);
      Object.assign(result, {
        mainInterval: interval,
        calendar: this.unspecifiedTimeInterval.calendar,
        name: interval.name,
        cls: interval.cls,
        startDate: interval.startDate,
        endDate: interval.endDate,
        prioritySortValue: interval.getPrioritySortValue() - 1,
        ...data
      });
    }
    return result;
  }
  getUnspecifiedTimeIntervalForWeek(week) {
    let result = this.intervalUnspecifiedTimeIntervals.get(week);
    if (!result) {
      const searchKey = week.compositeIntervalCode;
      result = this.weekUnspecifiedTimeIntervalsByKey.get(searchKey);
      if (!result) {
        result = this.unspecifiedTimeInterval.copy();
        this.intervalUnspecifiedTimeIntervals.set(week, result);
        this.weekUnspecifiedTimeIntervalsByKey.set(searchKey, result);
        Object.assign(result, {
          mainInterval: week,
          calendar: this.unspecifiedTimeInterval.calendar,
          name: week.name,
          cls: week.cls,
          startDate: week.startDate,
          endDate: week.endDate,
          prioritySortValue: week.getPrioritySortValue() - 1
        });
      }
    }
    return result;
  }
  clear() {
    this.staticIntervalsCached = false;
    super.clear();
  }
  cacheStaticIntervals() {
    var _a5;
    const timeZone = (_a5 = this.calendar.project) == null ? void 0 : _a5.timeZone;
    const intervalCache = this.intervalCache;
    for (let interval of this.intervalStore) {
      const isStatic = interval.isStatic();
      const isWeek = interval.type === CalendarIntervalType.Week;
      if (isStatic || isWeek) {
        if (isWeek) {
          interval = this.getUnspecifiedTimeIntervalForWeek(interval);
        }
        let { startDate, endDate } = interval;
        if (timeZone != null) {
          startDate = TimeZoneHelper.toTimeZone(startDate, timeZone);
          endDate = TimeZoneHelper.toTimeZone(endDate, timeZone);
        }
        intervalCache.addInterval(
          startDate || MIN_DATE2,
          endDate || MAX_DATE2,
          (existingCacheInterval) => existingCacheInterval.includeInterval(interval)
        );
      }
    }
  }
};

// ../Engine/lib/Engine/calendar/UnspecifiedTimeIntervalModel.js
var UnspecifiedTimeIntervalModel = class extends Mixin([CalendarIntervalMixin], (base) => {
  class UnspecifiedTimeIntervalModel2 extends base {
    getCalendar() {
      return this.calendar;
    }
    getMainInterval() {
      return this.mainInterval;
    }
    // NOTE: See parent class implementation for further comments
    getPrioritySortValue() {
      if (this.prioritySortValue != null)
        return this.prioritySortValue;
      return this.prioritySortValue = this.getCalendar().getDepth();
    }
  }
  UnspecifiedTimeIntervalModel2.$name = "UnspecifiedTimeIntervalModel";
  return UnspecifiedTimeIntervalModel2;
}) {
};

// ../Engine/lib/Engine/quark/model/AbstractCalendarMixin.js
var AbstractCalendarMixin = class extends Mixin([AbstractPartOfProjectModelMixin], (base) => {
  const superProto2 = base.prototype;
  class CalendarMixin extends base {
    constructor() {
      super(...arguments);
      this.version = 1;
    }
    static get fields() {
      return [
        { name: "version", type: "number" },
        "name",
        { name: "unspecifiedTimeIsWorking", type: "boolean", defaultValue: true },
        { name: "intervals", type: "store", subStore: true },
        "cls",
        "iconCls",
        { name: "treatInconsistentIntervals", persist: false }
      ];
    }
    get intervalStoreClass() {
      return CalendarIntervalStore;
    }
    get intervalStore() {
      return this.meta.intervalsStore;
    }
    // Not a typo, name is generated from the fields name = intervals
    initIntervalsStore(config) {
      config.storeClass = this.intervalStoreClass;
      config.modelClass = this.getDefaultConfiguration().calendarIntervalModelClass || this.intervalStoreClass.defaultConfig.modelClass;
      config.calendar = this;
      config.syncDataOnLoad = true;
    }
    // this method is called when the new value for the `intervals` field of this model is assigned
    // the type of the `intervals` field is "store" that's why this magic
    processIntervalsStoreData(intervals) {
      this.bumpVersion();
    }
    isDefault() {
      const project = this.getProject();
      if (project) {
        return this === project.defaultCalendar;
      }
      return false;
    }
    getDepth() {
      return this.childLevel + 1;
    }
    /**
     * The core iterator method of the calendar.
     *
     * @param options The options for iterator. Should contain at least one of the `startDate`/`endDate` properties
     * which indicates what timespan to examine for availability intervals. If one of boundaries is not provided
     * iterator function should return `false` at some point, to avoid infinite loops.
     *
     * Another recognized option is `isForward`, which indicates the direction in which to iterate through the timespan.
     *
     * @param func The iterator function to call. It will be called for every distinct set of availability intervals, found
     * in the given timespan. All the intervals, which are "active" for current interval are collected in the 3rd argument
     * for this function - [[CalendarCacheInterval|calendarCacheInterval]]. If iterator returns `false` (checked with `===`)
     * the iteration stops.
     *
     * @param scope The scope (`this` value) to execute the iterator in.
     */
    forEachAvailabilityInterval(options, func, scope) {
      var _a5, _b2;
      const maxRange = (_b2 = options.maxRange) != null ? _b2 : (_a5 = this.getProject()) == null ? void 0 : _a5.maxCalendarRange;
      if (maxRange) {
        options = { ...options, maxRange };
      }
      return this.calendarCache.forEachAvailabilityInterval(options, func, scope);
    }
    /**
     * This method starts at the given `date` and moves forward or backward in time, depending on `isForward`.
     * It stops moving as soon as it accumulates the `durationMs` milliseconds of working time and returns the date
     * at which it has stopped and remaining duration - the [[AccumulateWorkingTimeResult]] object.
     *
     * Normally, the remaining duration will be 0, indicating the full `durationMs` has been accumulated.
     * However, sometimes, calendar might not be able to accumulate enough working time due to various reasons,
     * like if it does not contain enough working time - this case will be indicated with remaining duration bigger than 0.
     *
     * @param date
     * @param durationMs
     * @param isForward
     */
    accumulateWorkingTime(date2, durationMs, isForward) {
      var _a5, _b2, _c2;
      if (durationMs === 0)
        return { finalDate: new Date(date2), remainingDurationInMs: 0 };
      if (isNaN(durationMs))
        throw new Error("Invalid duration");
      let finalDate = date2;
      const adjustDurationToDST = (_c2 = (_b2 = (_a5 = this.getProject()) == null ? void 0 : _a5.adjustDurationToDST) != null ? _b2 : this.adjustDurationToDST) != null ? _c2 : false;
      this.forEachAvailabilityInterval(isForward ? { startDate: date2, isForward: true } : { endDate: date2, isForward: false }, (intervalStartDate, intervalEndDate, calendarCacheInterval) => {
        let result = true;
        if (calendarCacheInterval.getIsWorking()) {
          let diff = intervalEndDate.getTime() - intervalStartDate.getTime();
          if (durationMs <= diff) {
            if (adjustDurationToDST) {
              const dstDiff = isForward ? intervalStartDate.getTimezoneOffset() - new Date(intervalStartDate.getTime() + durationMs).getTimezoneOffset() : new Date(intervalEndDate.getTime() - durationMs).getTimezoneOffset() - intervalEndDate.getTimezoneOffset();
              durationMs -= dstDiff * 60 * 1e3;
            }
            finalDate = isForward ? new Date(intervalStartDate.getTime() + durationMs) : new Date(intervalEndDate.getTime() - durationMs);
            durationMs = 0;
            result = false;
          } else {
            if (adjustDurationToDST) {
              const dstDiff = intervalStartDate.getTimezoneOffset() - intervalEndDate.getTimezoneOffset();
              diff += dstDiff * 60 * 1e3;
            }
            finalDate = isForward ? intervalEndDate : intervalStartDate;
            durationMs -= diff;
          }
        }
        return result;
      });
      return { finalDate: new Date(finalDate), remainingDurationInMs: durationMs };
    }
    /**
     * Calculate the working time duration between the 2 dates, in milliseconds.
     *
     * @param {Date} startDate
     * @param {Date} endDate
     * @param {Boolean} [allowNegative] Method ignores negative values by default, returning 0. Set to true to get
     * negative duration.
     */
    calculateDurationMs(startDate, endDate, allowNegative = false) {
      var _a5, _b2, _c2;
      let duration = 0;
      const multiplier = startDate.getTime() <= endDate.getTime() || !allowNegative ? 1 : -1;
      if (multiplier < 0) {
        [startDate, endDate] = [endDate, startDate];
      }
      const adjustDurationToDST = (_c2 = (_b2 = (_a5 = this.getProject()) == null ? void 0 : _a5.adjustDurationToDST) != null ? _b2 : this.adjustDurationToDST) != null ? _c2 : false;
      this.forEachAvailabilityInterval({ startDate, endDate }, (intervalStartDate, intervalEndDate, calendarCacheInterval) => {
        if (calendarCacheInterval.getIsWorking()) {
          duration += intervalEndDate.getTime() - intervalStartDate.getTime();
          if (adjustDurationToDST) {
            const dstDiff = intervalStartDate.getTimezoneOffset() - intervalEndDate.getTimezoneOffset();
            duration += dstDiff * 60 * 1e3;
          }
        }
      });
      return duration * multiplier;
    }
    /**
     * Calculate the end date of the time interval which starts at `startDate` and has `durationMs` working time duration
     * (in milliseconds).
     *
     * @param startDate
     * @param durationMs
     */
    calculateEndDate(startDate, durationMs) {
      const isForward = durationMs >= 0;
      const res = this.accumulateWorkingTime(startDate, Math.abs(durationMs), isForward);
      return res.remainingDurationInMs === 0 ? res.finalDate : null;
    }
    /**
     * Calculate the start date of the time interval which ends at `endDate` and has `durationMs` working time duration
     * (in milliseconds).
     *
     * @param endDate
     * @param durationMs
     */
    calculateStartDate(endDate, durationMs) {
      const isForward = durationMs <= 0;
      const res = this.accumulateWorkingTime(endDate, Math.abs(durationMs), isForward);
      return res.remainingDurationInMs === 0 ? res.finalDate : null;
    }
    /**
     * Returns the earliest point at which a working period of time starts, following the given date.
     * Can be the date itself, if it comes on the working time.
     *
     * @param date The date after which to skip the non-working time.
     * @param isForward Whether the "following" means forward in time or backward.
     */
    skipNonWorkingTime(date2, isForward = true) {
      let workingDate;
      const res = this.forEachAvailabilityInterval(isForward ? { startDate: date2, isForward: true } : { endDate: date2, isForward: false }, (intervalStartDate, intervalEndDate, calendarCacheInterval) => {
        if (calendarCacheInterval.getIsWorking()) {
          workingDate = isForward ? intervalStartDate : intervalEndDate;
          return false;
        }
      });
      if (res === CalendarIteratorResult.MaxRangeReached || res === CalendarIteratorResult.FullRangeIterated)
        return "empty_calendar";
      return workingDate ? new Date(workingDate) : new Date(date2);
    }
    /**
     * This method adds a single [[CalendarIntervalMixin]] to the internal collection of the calendar
     */
    addInterval(interval) {
      return this.addIntervals([interval])[0];
    }
    /**
     * This method adds an array of [[CalendarIntervalMixin]] to the internal collection of the calendar
     */
    addIntervals(intervals) {
      this.bumpVersion();
      return this.intervalStore.add(intervals);
    }
    /**
     * This method removes a single [[CalendarIntervalMixin]] from the internal collection of the calendar
     */
    removeInterval(interval) {
      return this.removeIntervals([interval]);
    }
    /**
     * This method removes an array of [[CalendarIntervalMixin]] from the internal collection of the calendar
     */
    removeIntervals(intervals) {
      this.bumpVersion();
      return this.intervalStore.remove(intervals);
    }
    /**
     * This method removes all intervals from the internal collection of the calendar
     */
    clearIntervals(silent) {
      if (!silent) {
        this.bumpVersion();
      }
      return this.intervalStore.removeAll(silent);
    }
    bumpVersion() {
      this.clearCache();
      this.version++;
    }
    get calendarCache() {
      if (this.$calendarCache !== void 0)
        return this.$calendarCache;
      const unspecifiedTimeInterval = new UnspecifiedTimeIntervalModel({
        isWorking: this.unspecifiedTimeIsWorking
      });
      unspecifiedTimeInterval.calendar = this;
      return this.$calendarCache = new CalendarCacheSingle({
        calendar: this,
        unspecifiedTimeInterval,
        intervalStore: this.intervalStore,
        treatInconsistentIntervals: this.treatInconsistentIntervals,
        parentCache: this.parent && !this.parent.isRoot ? this.parent.calendarCache : null
      });
    }
    clearCache() {
      this.$calendarCache && this.$calendarCache.clear();
      this.$calendarCache = void 0;
    }
    resetPriorityOfAllIntervals() {
      this.traverse((calendar) => {
        for (const interval of calendar.intervalStore)
          interval.resetPriority();
      });
    }
    insertChild(child, before, silent) {
      let res = superProto2.insertChild.call(this, ...arguments);
      if (!Array.isArray(res)) {
        res = [res];
      }
      res.forEach((r) => {
        r.bumpVersion();
        r.resetPriorityOfAllIntervals();
      });
      return res;
    }
    joinProject() {
      superProto2.joinProject.call(this);
      this.intervalStore.setProject(this.getProject());
    }
    leaveProject() {
      superProto2.leaveProject.call(this);
      this.intervalStore.setProject(null);
      this.clearCache();
    }
    doDestroy() {
      this.leaveProject();
      this.intervalStore.destroy();
      super.doDestroy();
    }
    isDayHoliday(day2) {
      const startDate = DateHelper.clearTime(day2), endDate = DateHelper.getNext(day2, TimeUnit.Day);
      let hasWorkingTime = false;
      this.forEachAvailabilityInterval({ startDate, endDate, isForward: true }, (_intervalStartDate, _intervalEndDate, calendarCacheInterval) => {
        hasWorkingTime = calendarCacheInterval.getIsWorking();
        return !hasWorkingTime;
      });
      return !hasWorkingTime;
    }
    getDayIntervals(day2) {
      const startDate = DateHelper.clearTime(day2), endDate = DateHelper.getNext(day2, TimeUnit.Day);
      let intervals = [];
      this.forEachAvailabilityInterval({ startDate, endDate, isForward: true }, (_intervalStartDate, _intervalEndDate, calendarCacheInterval) => intervals.push([_intervalStartDate, _intervalEndDate, calendarCacheInterval.intervals[0]]));
      return intervals;
    }
    getDailyHolidaysRanges(startDate, endDate) {
      const result = [];
      startDate = DateHelper.clearTime(startDate);
      while (startDate < endDate) {
        if (this.isDayHoliday(startDate)) {
          result.push({
            startDate,
            endDate: DateHelper.getStartOfNextDay(startDate, true, true)
          });
        }
        startDate = DateHelper.getNext(startDate, TimeUnit.Day);
      }
      return result;
    }
    /**
     * Returns working time ranges between the provided dates.
     * @param {Date} startDate Start of the period to get ranges from.
     * @param {Date} endDate End of the period to get ranges from.
     */
    getWorkingTimeRanges(startDate, endDate, maxRange) {
      const result = [];
      this.forEachAvailabilityInterval({ startDate, endDate, isForward: true, maxRange }, (intervalStartDate, intervalEndDate, calendarCacheInterval) => {
        if (calendarCacheInterval.getIsWorking()) {
          const entry = calendarCacheInterval.intervals[0];
          result.push({
            name: entry.name,
            startDate: intervalStartDate,
            endDate: intervalEndDate
          });
        }
      });
      return result;
    }
    /**
     * Returns non-working time ranges between the provided dates.
     * @param {Date} startDate Start of the period to get ranges from.
     * @param {Date} endDate End of the period to get ranges from.
     */
    getNonWorkingTimeRanges(startDate, endDate, maxRange) {
      const result = [];
      let lastEndDate;
      let lastInterval;
      let lastRange;
      this.forEachAvailabilityInterval({ startDate, endDate, isForward: true, maxRange }, (intervalStartDate, intervalEndDate, calendarCacheInterval) => {
        if (!calendarCacheInterval.getIsWorking()) {
          const interval = calendarCacheInterval.intervals[0];
          if (interval === lastInterval && lastEndDate.getTime() === intervalStartDate.getTime()) {
            lastRange.endDate = intervalEndDate;
          } else {
            lastRange = {
              name: interval.name,
              type: interval.type,
              iconCls: interval.iconCls,
              cls: interval.cls,
              startDate: intervalStartDate,
              endDate: intervalEndDate
            };
            result.push(lastRange);
          }
          lastInterval = interval;
          lastEndDate = intervalEndDate;
        }
      });
      return result;
    }
    /**
     * Checks if there is a working time interval in the provided time range (or when just startDate is provided,
     * checks if the date is contained inside a working time interval in this calendar)
     * @param startDate
     * @param [endDate]
     * @param [fullyContained] Pass true to check if the range is fully covered by a single continuous working time block
     */
    isWorkingTime(startDate, endDate, fullyContained) {
      if (fullyContained) {
        let found;
        const res = this.forEachAvailabilityInterval({ startDate, endDate, isForward: true }, (intervalStartDate, intervalEndDate, calendarCacheInterval) => {
          if (calendarCacheInterval.getIsWorking() && intervalStartDate <= startDate && intervalEndDate >= endDate) {
            found = true;
            return false;
          }
        });
        if (res === CalendarIteratorResult.MaxRangeReached || res === CalendarIteratorResult.FullRangeIterated)
          return false;
        return found;
      } else {
        const workingTimeStart = this.skipNonWorkingTime(startDate);
        return workingTimeStart && workingTimeStart !== "empty_calendar" ? endDate ? workingTimeStart < endDate : workingTimeStart.getTime() === startDate.getTime() : false;
      }
    }
  }
  return CalendarMixin;
}) {
};

// ../Engine/lib/Engine/quark/model/scheduler_core/CoreCalendarMixin.js
var CoreCalendarMixin = class extends Mixin([AbstractCalendarMixin, CorePartOfProjectModelMixin], (base) => {
  const superProto2 = base.prototype;
  class CoreCalendarMixin2 extends base {
  }
  return CoreCalendarMixin2;
}) {
};

// ../Engine/lib/Engine/quark/store/AbstractCalendarManagerStoreMixin.js
var AbstractCalendarManagerStoreMixin = class extends Mixin([AbstractPartOfProjectStoreMixin], (base) => {
  class AbstractCalendarManagerStoreMixin2 extends base {
    // special handling to destroy calendar models as part of destroying this store
    doDestroy() {
      var _a5;
      const records = [];
      if (!this.isChained && !((_a5 = this.rootNode) == null ? void 0 : _a5.isDestroyed)) {
        this.traverse((record) => records.push(record));
      }
      super.doDestroy();
      records.forEach((record) => record.destroy());
    }
  }
  return AbstractCalendarManagerStoreMixin2;
}) {
};

// ../Engine/lib/Engine/quark/store/CoreCalendarManagerStoreMixin.js
var CoreCalendarManagerStoreMixin = class extends Mixin([AbstractCalendarManagerStoreMixin, CorePartOfProjectStoreMixin], (base) => {
  const superProto2 = base.prototype;
  class CoreCalendarManagerStoreMixin2 extends base {
    static get defaultConfig() {
      return {
        tree: true,
        modelClass: CoreCalendarMixin
      };
    }
  }
  return CoreCalendarManagerStoreMixin2;
}) {
};

// ../Engine/lib/Engine/quark/model/AbstractProjectMixin.js
var EventsWrapper = class extends Mixin([], Events_default) {
};
var DelayableWrapper = class extends Mixin([], Delayable_default) {
};
var AbstractProjectMixin = class extends Mixin([
  EventsWrapper,
  DelayableWrapper,
  Model
], (base) => {
  const superProto2 = base.prototype;
  class AbstractProjectMixin2 extends base {
    constructor() {
      super(...arguments);
      this.isRestoringData = false;
    }
    get isRepopulatingStores() {
      return false;
    }
    get isInitialCommit() {
      return !this.isInitialCommitPerformed || this.hasLoadedDataToCommit;
    }
    construct(config = {}) {
      this.isInitialCommitPerformed = false;
      this.isLoadingInlineData = false;
      this.isWritingData = false;
      this.hasLoadedDataToCommit = false;
      const silenceInitialCommit = "silenceInitialCommit" in config ? config.silenceInitialCommit : true;
      const adjustDurationToDST = "adjustDurationToDST" in config ? config.adjustDurationToDST : false;
      this.maxCalendarRange = "maxCalendarRange" in config ? config.maxCalendarRange : 15768e7;
      delete config.maxCalendarRange;
      delete config.silenceInitialCommit;
      delete config.adjustDurationToDST;
      superProto2.construct.call(this, config);
      this.silenceInitialCommit = silenceInitialCommit;
      this.adjustDurationToDST = adjustDurationToDST;
    }
    // Template method called when a stores dataset is replaced. Implemented in SchedulerBasicProjectMixin
    repopulateStore(store) {
    }
    // Template method called when replica should be repopulated. Implemented in SchedulerBasicProjectMixin
    repopulateReplica() {
    }
    deferUntilRepopulationIfNeeded(deferId, func, args) {
      func(...args);
    }
    // Template method called when a store is attached to the project
    attachStore(store) {
    }
    // Template method called when a store is detached to the project
    detachStore(store) {
    }
    async commitAsync() {
      throw new Error("Abstract method called");
    }
    // Different implementations for Core and Basic engines
    isEngineReady() {
      throw new Error("Abstract method called");
    }
    getStm() {
      throw new Error("Abstract method called");
    }
  }
  return AbstractProjectMixin2;
}) {
};

// ../Engine/lib/Engine/quark/model/scheduler_core/SchedulerCoreProjectMixin.js
var DelayableWrapper2 = class extends Mixin([], Delayable_default) {
};
var SchedulerCoreProjectMixin = class extends Mixin([
  AbstractProjectMixin,
  CorePartOfProjectGenericMixin,
  DelayableWrapper2,
  Model
], (base) => {
  const superProto2 = base.prototype;
  class SchedulerCoreProjectMixin2 extends base {
    static get fields() {
      return [
        { name: "startDate", type: "date" },
        { name: "endDate", type: "date" }
      ];
    }
    static get configurable() {
      return {
        stm: {},
        eventStore: {},
        assignmentStore: {},
        resourceStore: {},
        dependencyStore: {},
        calendarManagerStore: {},
        eventModelClass: SchedulerCoreEvent,
        assignmentModelClass: CoreAssignmentMixin,
        resourceModelClass: CoreResourceMixin,
        dependencyModelClass: CoreDependencyMixin,
        calendarModelClass: CoreCalendarMixin,
        eventStoreClass: CoreEventStoreMixin,
        assignmentStoreClass: CoreAssignmentStoreMixin,
        resourceStoreClass: CoreResourceStoreMixin,
        dependencyStoreClass: CoreDependencyStoreMixin,
        calendarManagerStoreClass: CoreCalendarManagerStoreMixin,
        assignmentsData: null,
        calendarsData: null,
        dependenciesData: null,
        eventsData: null,
        resourcesData: null
      };
    }
    //endregion
    //region Init
    construct(config = {}) {
      var _a5;
      const me = this;
      me.$invalidated = /* @__PURE__ */ new Set();
      me.$leaving = /* @__PURE__ */ new Set();
      me.isPerformingCommit = false;
      me.silenceInitialCommit = true;
      me.ongoing = Promise.resolve();
      if (config.eventStore && !config.assignmentStore) {
        const eventStore = config.eventStore.masterStore || config.eventStore;
        const assignmentStore = eventStore.assignmentStore || ((_a5 = eventStore.crudManager) == null ? void 0 : _a5.assignmentStore);
        if (assignmentStore == null ? void 0 : assignmentStore.isAssignmentStore) {
          config.assignmentStore = assignmentStore;
          me.isSharingAssignmentStore = true;
        }
      }
      superProto2.construct.call(me, config);
      me.defaultCalendar = new me.calendarManagerStore.modelClass({
        unspecifiedTimeIsWorking: me.unspecifiedTimeIsWorking
      });
      me.defaultCalendar.project = me;
      const { calendarsData, eventsData, dependenciesData, resourcesData, assignmentsData } = me;
      const hasInlineData = Boolean(calendarsData || eventsData || dependenciesData || resourcesData || assignmentsData);
      if (hasInlineData) {
        me.loadInlineData({
          calendarsData,
          eventsData,
          dependenciesData,
          resourcesData,
          assignmentsData
        });
        delete me.calendarsData;
        delete me.eventsData;
        delete me.dependenciesData;
        delete me.resourcesData;
        delete me.assignmentsData;
      } else {
        me.bufferedCommitAsync();
      }
    }
    doDestroy() {
      var _a5, _b2, _c2, _d, _e, _f;
      const me = this;
      (_a5 = me.eventStore) == null ? void 0 : _a5.destroy();
      (_b2 = me.dependencyStore) == null ? void 0 : _b2.destroy();
      (_c2 = me.assignmentStore) == null ? void 0 : _c2.destroy();
      (_d = me.resourceStore) == null ? void 0 : _d.destroy();
      (_e = me.calendarManagerStore) == null ? void 0 : _e.destroy();
      me.defaultCalendar.destroy();
      (_f = me.stm) == null ? void 0 : _f.destroy();
      superProto2.doDestroy.call(this);
    }
    /**
     * This method loads the "raw" data into the project. The loading is basically happening by
     * assigning the individual data entries to the `data` property of the corresponding store.
     *
     * @param data
     */
    async loadInlineData(data) {
      const me = this;
      me.isLoadingInlineData = true;
      if (data.calendarsData) {
        me.calendarManagerStore.data = data.calendarsData;
      }
      if (data.resourcesData) {
        me.resourceStore.data = data.resourcesData;
      }
      if (data.assignmentsData) {
        me.assignmentStore.data = data.assignmentsData;
      }
      if (data.eventsData) {
        me.eventStore.data = data.eventsData;
      }
      if (data.tasksData) {
        me.eventStore.data = data.tasksData;
      }
      if (data.dependenciesData) {
        me.dependencyStore.data = data.dependenciesData;
      }
      await me.commitLoad();
      me.isLoadingInlineData = false;
      return;
    }
    //endregion
    //region Join
    async commitLoad() {
      await this.commitAsync();
      if (!this.isDestroyed)
        this.trigger("load");
    }
    //endregion
    //region EventStore
    resolveStoreAndModelClass(name, config) {
      const storeClass = (config == null ? void 0 : config.storeClass) || this[`${name}StoreClass`];
      let modelClass = config == null ? void 0 : config.modelClass;
      if (!modelClass) {
        if (this.getDefaultConfiguration()[`${name}ModelClass`] !== storeClass.getDefaultConfiguration().modelClass) {
          modelClass = storeClass.getDefaultConfiguration().modelClass;
        } else {
          modelClass = this[`${name}ModelClass`];
        }
      }
      return { storeClass, modelClass };
    }
    setEventStore(eventStore) {
      this.eventStore = eventStore;
    }
    changeEventStore(eventStore, oldStore) {
      const me = this, { stm } = me;
      me.detachStore(oldStore);
      if (!(eventStore instanceof Store)) {
        const { storeClass, modelClass } = me.resolveStoreAndModelClass("event", eventStore);
        eventStore = new storeClass(ObjectHelper.assign({
          modelClass,
          project: me,
          stm
        }, eventStore));
      } else {
        eventStore.project = me;
        stm.addStore(eventStore);
        eventStore.joinRecordsToProject(me);
      }
      if (oldStore && stm.hasStore(oldStore)) {
        stm.removeStore(oldStore);
        oldStore.unjoinRecordsFromProject(me);
        const { assignmentsForRemoval } = oldStore;
        assignmentsForRemoval.forEach((assignment) => {
          const oldEvent = assignment.event;
          if (oldEvent) {
            const newEvent = eventStore.getById(oldEvent.id);
            if (newEvent) {
              assignment.event = newEvent;
              assignmentsForRemoval.delete(assignment);
            }
          }
        });
        oldStore.afterEventRemoval();
      }
      eventStore.setProject(me);
      return eventStore;
    }
    updateEventStore(eventStore, oldStore) {
      this.attachStore(eventStore);
      this.trigger("eventStoreChange", { store: eventStore });
    }
    //endregion
    //region AssignmentStore
    setAssignmentStore(assignmentStore) {
      this.assignmentStore = assignmentStore;
    }
    changeAssignmentStore(assignmentStore, oldStore) {
      const me = this, { stm } = me;
      me.detachStore(oldStore);
      if (oldStore && stm.hasStore(oldStore)) {
        stm.removeStore(oldStore);
        oldStore.unjoinRecordsFromProject(me);
      }
      if (!(assignmentStore instanceof Store)) {
        const { storeClass, modelClass } = me.resolveStoreAndModelClass("assignment", assignmentStore);
        assignmentStore = new storeClass(ObjectHelper.assign({
          modelClass,
          project: me,
          stm
        }, assignmentStore));
      } else {
        assignmentStore.project = me;
        stm.addStore(assignmentStore);
        assignmentStore.joinRecordsToProject(me);
      }
      assignmentStore.setProject(me);
      return assignmentStore;
    }
    updateAssignmentStore(assignmentStore, oldStore) {
      this.attachStore(assignmentStore);
      this.trigger("assignmentStoreChange", { store: assignmentStore });
    }
    //endregion
    //region ResourceStore
    setResourceStore(resourceStore) {
      this.resourceStore = resourceStore;
    }
    changeResourceStore(resourceStore, oldStore) {
      const me = this, { stm } = me;
      me.detachStore(oldStore);
      if (!(resourceStore instanceof Store)) {
        const { storeClass, modelClass } = me.resolveStoreAndModelClass("resource", resourceStore);
        resourceStore = new storeClass(ObjectHelper.assign({
          modelClass,
          project: me,
          stm
        }, resourceStore));
      } else {
        resourceStore.project = me;
        stm.addStore(resourceStore);
        resourceStore.joinRecordsToProject(me);
      }
      if (oldStore && stm.hasStore(oldStore)) {
        stm.removeStore(oldStore);
        oldStore.unjoinRecordsFromProject(me);
        const { assignmentsForRemoval } = oldStore;
        assignmentsForRemoval.forEach((assignment) => {
          const oldResource = assignment.resource;
          if (oldResource) {
            const newResource = resourceStore.getById(oldResource.id);
            if (newResource) {
              assignment.resource = newResource;
              assignmentsForRemoval.delete(assignment);
            }
          }
        });
        oldStore.afterResourceRemoval();
      }
      resourceStore.setProject(me);
      return resourceStore;
    }
    updateResourceStore(resourceStore, oldStore) {
      this.attachStore(resourceStore);
      this.trigger("resourceStoreChange", { store: resourceStore });
    }
    //endregion
    //region DependencyStore
    setDependencyStore(dependencyStore) {
      this.dependencyStore = dependencyStore;
    }
    changeDependencyStore(dependencyStore, oldStore) {
      const me = this;
      me.detachStore(oldStore);
      if (!(dependencyStore instanceof Store)) {
        const { storeClass, modelClass } = me.resolveStoreAndModelClass("dependency", dependencyStore);
        dependencyStore = new storeClass(ObjectHelper.assign({
          modelClass,
          project: me,
          stm: me.stm
        }, dependencyStore));
      } else {
        dependencyStore.project = me;
        me.stm.addStore(dependencyStore);
        dependencyStore.joinRecordsToProject(me);
      }
      return dependencyStore;
    }
    updateDependencyStore(dependencyStore, oldStore) {
      this.attachStore(dependencyStore);
      this.trigger("dependencyStoreChange", { store: dependencyStore });
    }
    //endregion
    //region CalendarManagerStore
    setCalendarManagerStore(calendarManagerStore) {
      this.calendarManagerStore = calendarManagerStore;
    }
    changeCalendarManagerStore(calendarManagerStore, oldStore) {
      const me = this;
      me.detachStore(oldStore);
      if (!(calendarManagerStore instanceof Store)) {
        const storeClass = (calendarManagerStore == null ? void 0 : calendarManagerStore.storeClass) || me.calendarManagerStoreClass;
        const modelClass = (calendarManagerStore == null ? void 0 : calendarManagerStore.modelClass) || storeClass.getDefaultConfiguration().modelClass || me.calendarModelClass;
        calendarManagerStore = new storeClass(ObjectHelper.assign({
          modelClass,
          project: me,
          stm: me.stm
        }, calendarManagerStore));
      } else {
        me.stm.addStore(calendarManagerStore);
      }
      calendarManagerStore.setProject(me);
      return calendarManagerStore;
    }
    updateCalendarManagerStore(calendarManagerStore, oldStore) {
      this.attachStore(calendarManagerStore);
      this.trigger("calendarManagerStoreChange", { store: calendarManagerStore });
    }
    //endregion
    //region Calendar
    get calendar() {
      return this.$calendar || this.defaultCalendar;
    }
    set calendar(calendar) {
      this.$calendar = calendar;
    }
    get effectiveCalendar() {
      return this.calendar;
    }
    //endregion
    //region Add records
    async addEvent(event) {
      this.eventStore.add(event);
      return this.commitAsync();
    }
    async addAssignment(assignment) {
      this.assignmentStore.add(assignment);
      return this.commitAsync();
    }
    async addResource(resource) {
      this.resourceStore.add(resource);
      return this.commitAsync();
    }
    async addDependency(dependency) {
      this.dependencyStore.add(dependency);
      return this.commitAsync();
    }
    //endregion
    //region Auto commit
    // Buffer commitAsync using setTimeout. Not using `buffer` on purpose, for performance reasons and to better
    // mimic how graph does it
    bufferedCommitAsync() {
      if (!this.hasPendingAutoCommit) {
        this.setTimeout({
          fn: () => {
            var _a5;
            return (_a5 = this.commitAsync) == null ? void 0 : _a5.call(this);
          },
          name: "commitAsync",
          delay: 10
        });
      }
    }
    get hasPendingAutoCommit() {
      return this.hasTimeout("commitAsync");
    }
    unScheduleAutoCommit() {
      this.clearTimeout("commitAsync");
    }
    //endregion
    //region Commit
    async commitAsync() {
      if (this.isPerformingCommit)
        return this.ongoing;
      this.trigger("beforeCommit");
      return this.ongoing = this.doCommitAsync();
    }
    async doCommitAsync() {
      const me = this;
      if (!me.$invalidated.size && !me.$leaving.size && !me.hasLoadedDataToCommit && !me.isInitialCommit) {
        me.trigger("commitFinalized");
        return;
      }
      me.$leaving.clear();
      me.isPerformingCommit = true;
      await delay(0);
      if (!me.isDestroyed) {
        me.unScheduleAutoCommit();
        for (const record of me.$invalidated) {
          record.calculateInvalidated();
        }
        const { isInitialCommit, silenceInitialCommit } = me;
        const silenceCommit = isInitialCommit && silenceInitialCommit;
        me.assignmentStore.onCommitAsync();
        me.dependencyStore.onCommitAsync();
        me.isInitialCommitPerformed = true;
        me.hasLoadedDataToCommit = false;
        me.isPerformingCommit = false;
        const stores2 = [me.assignmentStore, me.dependencyStore, me.eventStore, me.resourceStore, me.calendarManagerStore];
        stores2.forEach((store) => {
          var _a5, _b2;
          (_a5 = store.suspendAutoCommit) == null ? void 0 : _a5.call(store);
          (_b2 = store.suspendSortOnUpdate) == null ? void 0 : _b2.call(store);
        });
        me.isWritingData = true;
        me.trigger("refresh", { isInitialCommit, isCalculated: true });
        if (silenceCommit) {
          for (const record of me.$invalidated) {
            record.finalizeInvalidated(true);
          }
        } else {
          for (const record of me.$invalidated) {
            record.beginBatch(true);
            record.finalizeInvalidated();
          }
          for (const record of me.$invalidated) {
            record.endBatch(false, true);
          }
        }
        me.isWritingData = false;
        me.$invalidated.clear();
        me.trigger("dataReady");
        stores2.forEach((store) => {
          var _a5, _b2;
          (_a5 = store.resumeAutoCommit) == null ? void 0 : _a5.call(store);
          (_b2 = store.resumeSortOnUpdate) == null ? void 0 : _b2.call(store);
        });
        me.trigger("commitFinalized");
        return true;
      }
    }
    async propagateAsync() {
      return this.commitAsync();
    }
    // Called when a record invalidates itself, queues it for calculation
    invalidate(record) {
      this.$invalidated.add(record);
      this.bufferedCommitAsync();
    }
    // this does not account for possible scheduling conflicts
    async isValidDependency() {
      return true;
    }
    //endregion
    //region STM
    getStm() {
      return this.stm;
    }
    /**
     * State tracking manager instance the project relies on
     */
    set stm(stm) {
      stm = this.$stm = new StateTrackingManager(ObjectHelper.assign({
        disabled: true
      }, stm));
      stm.ion({
        // Propagate on undo/redo
        restoringStop: async () => {
          stm.disable();
          await this.commitAsync();
          if (!this.isDestroyed) {
            stm.enable();
            this.trigger("stateRestoringDone");
          }
        }
      });
    }
    get stm() {
      return this.$stm;
    }
    //endregion
    isEngineReady() {
      return !this.hasPendingAutoCommit && !this.isPerformingCommit && this.isInitialCommitPerformed;
    }
  }
  SchedulerCoreProjectMixin2.$name = "SchedulerCoreProjectMixin";
  SchedulerCoreProjectMixin2.applyConfigs = true;
  return SchedulerCoreProjectMixin2;
}) {
};

// ../Scheduler/lib/Scheduler/model/ProjectModel.js
var EngineMixin9 = SchedulerCoreProjectMixin;
var ProjectModel = class extends ProjectCurrentConfig_default(ProjectModelMixin_default(EngineMixin9)) {
  //region Inline data configs & properties
  /**
   * @hidefields id, readOnly, children, parentId, parentIndex
   */
  /**
   * A flag, indicating whether the dates and duration calculations should adjust the result to DST time shift.
   *
   * @config {Boolean} adjustDurationToDST
   * @default false
   */
  /**
   * Get/set {@link #property-eventStore} data.
   *
   * Always returns an array of {@link Scheduler.model.EventModel EventModels} but also accepts an array of
   * its configuration objects as input.
   *
   * @member {Scheduler.model.EventModel[]} events
   * @accepts {Scheduler.model.EventModel[]|EventModelConfig[]}
   * @category Inline data
   */
  /**
   * Data use to fill the {@link #property-eventStore}. Should be an array of
   * {@link Scheduler.model.EventModel EventModels} or its configuration objects.
   *
   * @config {Scheduler.model.EventModel[]|EventModelConfig[]} events
   * @category Inline data
   */
  /**
   * Get/set {@link #property-resourceStore} data.
   *
   * Always returns an array of {@link Scheduler.model.ResourceModel ResourceModels} but also accepts an array
   * of its configuration objects as input.
   *
   * @member {Scheduler.model.ResourceModel[]} resources
   * @accepts {Scheduler.model.ResourceModel[]|ResourceModelConfig[]}
   * @category Inline data
   */
  /**
   * Data use to fill the {@link #property-resourceStore}. Should be an array of
   * {@link Scheduler.model.ResourceModel ResourceModels} or its configuration objects.
   *
   * @config {Scheduler.model.ResourceModel[]|ResourceModelConfig[]} resources
   * @category Inline data
   */
  /**
   * Get/set {@link #property-assignmentStore} data.
   *
   * Always returns an array of {@link Scheduler.model.AssignmentModel AssignmentModels} but also accepts an
   * array of its configuration objects as input.
   *
   * @member {Scheduler.model.AssignmentModel[]} assignments
   * @accepts {Scheduler.model.AssignmentModel[]|AssignmentModelConfig[]}
   * @category Inline data
   */
  /**
   * Data use to fill the {@link #property-assignmentStore}. Should be an array of
   * {@link Scheduler.model.AssignmentModel AssignmentModels} or its configuration objects.
   *
   * @config {Scheduler.model.AssignmentModel[]|AssignmentModelConfig[]} assignments
   * @category Inline data
   */
  /**
   * Get/set {@link #property-dependencyStore} data.
   *
   * Always returns an array of {@link Scheduler.model.DependencyModel DependencyModels} but also accepts an
   * array of its configuration objects as input.
   *
   * @member {Scheduler.model.DependencyModel[]} dependencies
   * @accepts {Scheduler.model.DependencyModel[]|DependencyModelConfig[]}
   * @category Inline data
   */
  /**
   * Data use to fill the {@link #property-dependencyStore}. Should be an array of
   * {@link Scheduler.model.DependencyModel DependencyModels} or its configuration objects.
   *
   * @config {Scheduler.model.DependencyModel[]|DependencyModelConfig[]} dependencies
   * @category Inline data
   */
  /**
   * Get/set {@link #property-timeRangeStore} data.
   *
   * Always returns an array of {@link Scheduler.model.TimeRangeModel TimeRangeModels} but also accepts an
   * array of its configuration objects as input.
   *
   * @member {Scheduler.model.TimeRangeModel[]} timeRanges
   * @accepts {Scheduler.model.TimeRangeModel[]|TimeRangeModelConfig[]}
   * @category Inline data
   */
  /**
   * Data use to fill the {@link #property-timeRangeStore}. Should be an array of
   * {@link Scheduler.model.TimeRangeModel TimeRangeModels} or its configuration objects.
   *
   * @config {Scheduler.model.TimeRangeModel[]|TimeRangeModelConfig[]} timeRanges
   * @category Inline data
   */
  /**
   * Get/set {@link #property-resourceTimeRangeStore} data.
   *
   * Always returns an array of {@link Scheduler.model.ResourceTimeRangeModel ResourceTimeRangeModels} but
   * also accepts an array of its configuration objects as input.
   *
   * @member {Scheduler.model.ResourceTimeRangeModel[]} resourceTimeRanges
   * @accepts {Scheduler.model.ResourceTimeRangeModel[]|ResourceTimeRangeModelConfig[]}
   * @category Inline data
   */
  /**
   * Data use to fill the {@link #property-resourceTimeRangeStore}. Should be an array
   * of {@link Scheduler.model.ResourceTimeRangeModel ResourceTimeRangeModels} or its configuration objects.
   *
   * @config {Scheduler.model.ResourceTimeRangeModel[]|ResourceTimeRangeModelConfig[]} resourceTimeRanges
   * @category Inline data
   */
  //endregion
  //region Legacy inline data configs & properties
  /**
   * The initial data, to fill the {@link #property-eventStore} with.
   * Should be an array of {@link Scheduler.model.EventModel EventModels} or its configuration objects.
   *
   * @config {Scheduler.model.EventModel[]|EventModelConfig[]} eventsData
   * @category Legacy inline data
   */
  /**
   * The initial data, to fill the {@link #property-dependencyStore} with.
   * Should be an array of {@link Scheduler.model.DependencyModel DependencyModels} or its configuration
   * objects.
   *
   * @config {Scheduler.model.DependencyModel[]|DependencyModelConfig[]} [dependenciesData]
   * @category Legacy inline data
   */
  /**
   * The initial data, to fill the {@link #property-resourceStore} with.
   * Should be an array of {@link Scheduler.model.ResourceModel ResourceModels} or its configuration objects.
   *
   * @config {Scheduler.model.ResourceModel[]|ResourceModelConfig[]} [resourcesData]
   * @category Legacy inline data
   */
  /**
   * The initial data, to fill the {@link #property-assignmentStore} with.
   * Should be an array of {@link Scheduler.model.AssignmentModel AssignmentModels} or its configuration
   * objects.
   *
   * @config {Scheduler.model.AssignmentModel[]|AssignmentModelConfig[]} [assignmentsData]
   * @category Legacy inline data
   */
  //endregion
  //region Store configs & properties
  /**
   * The {@link Scheduler.data.EventStore store} holding the events information.
   *
   * See also {@link Scheduler.model.EventModel}
   *
   * @member {Scheduler.data.EventStore} eventStore
   * @category Models & Stores
   */
  /**
   * An {@link Scheduler.data.EventStore} instance or a config object.
   * @config {Scheduler.data.EventStore|EventStoreConfig} eventStore
   * @category Models & Stores
   */
  /**
   * The {@link Scheduler.data.DependencyStore store} holding the dependencies information.
   *
   * See also {@link Scheduler.model.DependencyModel}
   *
   * @member {Scheduler.data.DependencyStore} dependencyStore
   * @category Models & Stores
   */
  /**
   * A {@link Scheduler.data.DependencyStore} instance or a config object.
   * @config {Scheduler.data.DependencyStore|DependencyStoreConfig} dependencyStore
   * @category Models & Stores
   */
  /**
   * The {@link Scheduler.data.ResourceStore store} holding the resources that can be assigned to the events in the event store.
   *
   * See also {@link Scheduler.model.ResourceModel}
   *
   * @member {Scheduler.data.ResourceStore} resourceStore
   * @category Models & Stores
   */
  /**
   * A {@link Scheduler.data.ResourceStore} instance or a config object.
   * @config {Scheduler.data.ResourceStore|ResourceStoreConfig} resourceStore
   * @category Models & Stores
   */
  /**
   * The {@link Scheduler.data.AssignmentStore store} holding the assignments information.
   *
   * See also {@link Scheduler.model.AssignmentModel}
   *
   * @member {Scheduler.data.AssignmentStore} assignmentStore
   * @category Models & Stores
   */
  /**
   * An {@link Scheduler.data.AssignmentStore} instance or a config object.
   * @config {Scheduler.data.AssignmentStore|AssignmentStoreConfig} assignmentStore
   * @category Models & Stores
   */
  //endregion
  //region Configs
  static get defaultConfig() {
    return {
      /**
       * The constructor of the event model class, to be used in the project. Will be set as the
       * {@link Core.data.Store#config-modelClass} property of the {@link #property-eventStore}
       *
       * @config {Scheduler.model.EventModel}
       * @typings {typeof EventModel}
       * @category Models & Stores
       */
      eventModelClass: EventModel,
      /**
       * The constructor of the dependency model class, to be used in the project. Will be set as the
       * {@link Core.data.Store#config-modelClass} property of the {@link #property-dependencyStore}
       *
       * @config {Scheduler.model.DependencyModel}
       * @typings {typeof DependencyModel}
       * @category Models & Stores
       */
      dependencyModelClass: DependencyModel,
      /**
       * The constructor of the resource model class, to be used in the project. Will be set as the
       * {@link Core.data.Store#config-modelClass} property of the {@link #property-resourceStore}
       *
       * @config {Scheduler.model.ResourceModel}
       * @typings {typeof ResourceModel}
       * @category Models & Stores
       */
      resourceModelClass: ResourceModel,
      /**
       * The constructor of the assignment model class, to be used in the project. Will be set as the
       * {@link Core.data.Store#config-modelClass} property of the {@link #property-assignmentStore}
       *
       * @config {Scheduler.model.AssignmentModel}
       * @typings {typeof AssignmentModel}
       * @category Models & Stores
       */
      assignmentModelClass: AssignmentModel,
      /**
       * The constructor to create an event store instance with. Should be a class, subclassing the
       * {@link Scheduler.data.EventStore}
       * @config {Scheduler.data.EventStore|Object}
       * @typings {typeof EventStore|object}
       * @category Models & Stores
       */
      eventStoreClass: EventStore,
      /**
       * The constructor to create a dependency store instance with. Should be a class, subclassing the
       * {@link Scheduler.data.DependencyStore}
       * @config {Scheduler.data.DependencyStore|Object}
       * @typings {typeof DependencyStore|object}
       * @category Models & Stores
       */
      dependencyStoreClass: DependencyStore,
      /**
       * The constructor to create a resource store instance with. Should be a class, subclassing the
       * {@link Scheduler.data.ResourceStore}
       * @config {Scheduler.data.ResourceStore|Object}
       * @typings {typeof ResourceStore|object}
       * @category Models & Stores
       */
      resourceStoreClass: ResourceStore,
      /**
       * The constructor to create an assignment store instance with. Should be a class, subclassing the
       * {@link Scheduler.data.AssignmentStore}
       * @config {Scheduler.data.AssignmentStore|Object}
       * @typings {typeof AssignmentStore|object}
       * @category Models & Stores
       */
      assignmentStoreClass: AssignmentStore
    };
  }
  //endregion
  //region Events
  /**
   * Fired when the engine has finished its calculations and the results has been written back to the records.
   *
   * ```javascript
   * scheduler.project.on({
   *     dataReady() {
   *        console.log('Calculations finished');
   *     }
   * });
   *
   * scheduler.eventStore.first.duration = 10;
   *
   * // At some point a bit later it will log 'Calculations finished'
   * ```
   *
   * @event dataReady
   * @param {Scheduler.model.ProjectModel} source The project
   * @typings source -> {Scheduler.model.ProjectModel|any}
   * @param {Boolean} isInitialCommit Flag that shows if this commit is initial
   * @param {Set} records Set of all {@link Core.data.Model}s that were modified in the completed transaction.
   * Use the {@link Core.data.Model#property-modifications} property of each Model to identify
   * modified fields.
   */
  //endregion
  /**
   * Silences propagations caused by the project loading.
   *
   * Applying the loaded data to the project occurs in two basic stages:
   *
   * 1. Data gets into the engine graph which triggers changes propagation
   * 2. The changes caused by the propagation get written to related stores
   *
   * Setting this flag to `true` makes the component perform step 2 silently without triggering events causing reactions on those changes
   * (like sending changes back to the server if `autoSync` is enabled) and keeping stores in unmodified state.
   *
   * This is safe if the loaded data is consistent so propagation doesn't really do any adjustments.
   * By default the system treats the data as consistent so this option is `true`.
   *
   * ```javascript
   * new Scheduler({
   *     project : {
   *         // We want scheduling engine to recalculate the data properly
   *         // so then we could save it back to the server
   *         silenceInitialCommit : false
   *     }
   *     ...
   * })
   * ```
   *
   * @config {Boolean} silenceInitialCommit
   * @default true
   * @category Advanced
   */
  construct(...args) {
    super.construct(...args);
    if (VersionHelper.isTestEnv) {
      globalThis.bryntum.testProject = this;
    }
    this.modelPersistencyManager = this.createModelPersistencyManager();
  }
  /**
   * Creates and returns model persistency manager
   *
   * @returns {Scheduler.data.util.ModelPersistencyManager}
   * @internal
   */
  createModelPersistencyManager() {
    return new ModelPersistencyManager({
      eventStore: this,
      resourceStore: this.resourceStore,
      assignmentStore: this.assignmentStore,
      dependencyStore: this.dependencyStore
    });
  }
  doDestroy() {
    this.modelPersistencyManager.destroy();
    super.doDestroy();
  }
  // To comply with TaskBoards expectations
  get taskStore() {
    return this.eventStore;
  }
};
__publicField(ProjectModel, "$name", "ProjectModel");
ProjectModel.applyConfigs = true;
ProjectModel.initClass();
ProjectModel._$name = "ProjectModel";

// ../Scheduler/lib/Scheduler/data/CrudManager.js
var CrudManager = class extends AbstractCrudManager.mixin(ProjectCrudManager_default, AjaxTransport_default, JsonEncoder_default, LazyLoadCrudManager_default) {
  //region Config
  static get defaultConfig() {
    return {
      projectClass: ProjectModel,
      resourceStoreClass: ResourceStore,
      eventStoreClass: EventStore,
      assignmentStoreClass: AssignmentStore,
      dependencyStoreClass: DependencyStore,
      resourceTimeRangeStoreClass: ResourceTimeRangeStore,
      /**
       * A store with resources (or a config object).
       * @config {Scheduler.data.ResourceStore|ResourceStoreConfig}
       */
      resourceStore: {},
      /**
       * A store with events (or a config object).
       *
       * ```
       * crudManager : {
       *      eventStore {
       *          storeClass : MyEventStore
       *      }
       * }
       * ```
       * @config {Scheduler.data.EventStore|EventStoreConfig}
       */
      eventStore: {},
      /**
       * A store with assignments (or a config object).
       * @config {Scheduler.data.AssignmentStore|AssignmentStoreConfig}
       */
      assignmentStore: {},
      /**
       * A store with dependencies(or a config object).
       * @config {Scheduler.data.DependencyStore|DependencyStoreConfig}
       */
      dependencyStore: {},
      /**
       * A project that holds and links stores
       * @config {Scheduler.model.ProjectModel}
       */
      project: null,
      /**
       * A store with resource time ranges (or a config object).
       * @config {Scheduler.data.ResourceTimeRangeStore|ResourceTimeRangeStoreConfig}
       */
      resourceTimeRangeStore: {}
    };
  }
  //endregion
  buildProject() {
    return new this.projectClass(this.buildProjectConfig());
  }
  buildProjectConfig() {
    return ObjectHelper.cleanupProperties({
      eventStore: this.eventStore,
      resourceStore: this.resourceStore,
      assignmentStore: this.assignmentStore,
      dependencyStore: this.dependencyStore,
      resourceTimeRangeStore: this.resourceTimeRangeStore
    });
  }
  //region Stores
  set project(project) {
    const me = this;
    if (project !== me._project) {
      me.detachListeners("beforeDataReady");
      me.detachListeners("afterDataReady");
      me._project = project;
      if (project) {
        me.eventStore = project.eventStore;
        me.resourceStore = project.resourceStore;
        me.assignmentStore = project.assignmentStore;
        me.dependencyStore = project.dependencyStore;
        me.timeRangeStore = project.timeRangeStore;
        me.resourceTimeRangeStore = project.resourceTimeRangeStore;
        project.ion({
          name: "beforeDataReady",
          dataReady: () => me.suspendChangeTracking(),
          prio: 100,
          thisObj: me
        });
        project.ion({
          name: "afterDataReady",
          dataReady: () => me.resumeChangeTracking(),
          prio: -100,
          thisObj: me
        });
      }
      if (!me.eventStore) {
        me.eventStore = {};
      }
      if (!me.resourceStore) {
        me.resourceStore = {};
      }
      if (!me.assignmentStore) {
        me.assignmentStore = {};
      }
      if (!me.dependencyStore) {
        me.dependencyStore = {};
      }
      if (!me.resourceTimeRangeStore) {
        me.resourceTimeRangeStore = {};
      }
    }
  }
  get project() {
    return this._project;
  }
  /**
   * Store for {@link Scheduler/feature/TimeRanges timeRanges} feature.
   * @property {Core.data.Store}
   */
  get timeRangeStore() {
    var _a5;
    return (_a5 = this._timeRangeStore) == null ? void 0 : _a5.store;
  }
  set timeRangeStore(store) {
    var _a5;
    this.setFeaturedStore("_timeRangeStore", store, (_a5 = this.project) == null ? void 0 : _a5.timeRangeStoreClass);
  }
  /**
   * Store for {@link Scheduler/feature/ResourceTimeRanges resourceTimeRanges} feature.
   * @property {Core.data.Store}
   */
  get resourceTimeRangeStore() {
    var _a5;
    return (_a5 = this._resourceTimeRangeStore) == null ? void 0 : _a5.store;
  }
  set resourceTimeRangeStore(store) {
    this.setFeaturedStore("_resourceTimeRangeStore", store, this.resourceTimeRangeStoreClass);
  }
  /**
   * Get/set the resource store bound to the CRUD manager.
   * @property {Scheduler.data.ResourceStore}
   */
  get resourceStore() {
    var _a5;
    return (_a5 = this._resourceStore) == null ? void 0 : _a5.store;
  }
  set resourceStore(store) {
    this.setFeaturedStore("_resourceStore", store, this.resourceStoreClass);
  }
  /**
   * Get/set the event store bound to the CRUD manager.
   * @property {Scheduler.data.EventStore}
   */
  get eventStore() {
    var _a5;
    return (_a5 = this._eventStore) == null ? void 0 : _a5.store;
  }
  set eventStore(store) {
    this.setFeaturedStore("_eventStore", store, this.eventStoreClass);
  }
  /**
   * Get/set the assignment store bound to the CRUD manager.
   * @property {Scheduler.data.AssignmentStore}
   */
  get assignmentStore() {
    var _a5;
    return (_a5 = this._assignmentStore) == null ? void 0 : _a5.store;
  }
  set assignmentStore(store) {
    this.setFeaturedStore("_assignmentStore", store, this.assignmentStoreClass);
  }
  /**
   * Get/set the dependency store bound to the CRUD manager.
   * @property {Scheduler.data.DependencyStore}
   */
  get dependencyStore() {
    var _a5;
    return (_a5 = this._dependencyStore) == null ? void 0 : _a5.store;
  }
  set dependencyStore(store) {
    this.setFeaturedStore("_dependencyStore", store, this.dependencyStoreClass);
  }
  setFeaturedStore(property, store, storeClass) {
    var _a5;
    const me = this, oldStore = (_a5 = me[property]) == null ? void 0 : _a5.store;
    if (oldStore !== store) {
      store = Store.getStore(store, (store == null ? void 0 : store.storeClass) || storeClass);
      if (oldStore) {
        me.removeStore(oldStore);
      }
      me[property] = store && { store } || null;
      me.addPrioritizedStore(me[property]);
    }
    return me[property];
  }
  getChangesetPackage() {
    var _a5, _b2;
    const me = this, pack = super.getChangesetPackage();
    if (pack && (me.eventStore.usesSingleAssignment || ((_b2 = (_a5 = me.eventStore.modelClass.fieldMap) == null ? void 0 : _a5.resourceIds) == null ? void 0 : _b2.persist))) {
      delete pack[me.assignmentStore.id];
      if (!me.forceSync && !me.crudStores.some((storeInfo) => pack[storeInfo.storeId])) {
        return null;
      }
    }
    return pack;
  }
  //endregion
  get crudLoadValidationMandatoryStores() {
    return [this._eventStore.storeId, this._resourceStore.storeId];
  }
};
__publicField(CrudManager, "$name", "CrudManager");
CrudManager._$name = "CrudManager";

// ../Scheduler/lib/Scheduler/view/mixin/CurrentConfig.js
var stores = [
  "eventStore",
  "taskStore",
  "assignmentStore",
  "resourceStore",
  "dependencyStore",
  "timeRangeStore",
  "resourceTimeRangeStore"
];
var inlineProperties = [
  "events",
  "tasks",
  "resources",
  "assignments",
  "dependencies",
  "timeRanges",
  "resourceTimeRanges"
];
var CurrentConfig_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    preProcessCurrentConfigs(configs) {
      for (const prop of inlineProperties) {
        delete configs[prop];
      }
      super.preProcessCurrentConfigs(configs);
    }
    // This function is not meant to be called by any code other than Base#getCurrentConfig().
    getCurrentConfig(options) {
      var _a6, _b2;
      const project = (_b2 = (_a6 = this.project).getCurrentConfig) == null ? void 0 : _b2.call(_a6, options), result = super.getCurrentConfig(options);
      if (project) {
        result.project = project;
        const { crudManager } = result;
        if (crudManager) {
          for (const store of stores) {
            if (crudManager[store]) {
              project[store] = crudManager[store];
            }
          }
        }
        if (Object.keys(project).length === 0) {
          delete result.project;
        }
      }
      delete result.data;
      delete result.store;
      delete result.eventStore;
      delete result.resourceStore;
      delete result.crudManager;
      return result;
    }
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "CurrentConfig"), _a5;
};

// ../Scheduler/lib/Scheduler/view/mixin/Describable.js
var arrayify = (format2) => !format2 || Array.isArray(format2) ? format2 : [format2];
var pickFormat = (formats2, index, defaultFormat) => formats2 && formats2[index] !== true ? formats2[index] : defaultFormat;
var Describable_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    /**
     * Returns the date or ranges of included dates as an array. If there is only one significant date, the array will
     * have only one element. Otherwise, a range of dates is returned as a two-element array with `[0]` being the
     * `startDate` and `[1]` the `lastDate`.
     * @member {Date[]}
     * @internal
     */
    get dateBounds() {
      return [this.date];
    }
    /**
     * The textual description generated by the {@link #config-descriptionRenderer} if present, or by the
     * view's date (or date *range* if it has a range) and the {@link #config-descriptionFormat}.
     * @property {String}
     * @readonly
     */
    get description() {
      const me = this, { descriptionRenderer } = me;
      return descriptionRenderer ? me.callback(descriptionRenderer, me, [me]) : me.formattedDescription;
    }
    /**
     * Yields the default description value as calculated using the {@link #property-dateFormat} which
     * describes this object if no {@link #config-descriptionRenderer} was provided.
     *
     * This may be used in a configured {@link #config-descriptionRenderer} to augment the calculated
     * description.
     * @property {String}
     * @readonly
     */
    get formattedDescription() {
      var _a6, _b2;
      if (((_a6 = this.range) == null ? void 0 : _a6.magnitude) === 1 && this.range.unit !== "day") {
        return this.rangeDescription;
      } else {
        const me = this, { dateBounds, dateFormat } = me, descriptionFormat = (_b2 = me.descriptionFormat) != null ? _b2 : arrayify(me.defaultDescriptionFormat), format0 = pickFormat(descriptionFormat, 0, dateFormat), end = dateBounds.length > 1 && (descriptionFormat == null ? void 0 : descriptionFormat.length) > 1 && DateHelper.format(dateBounds[0], format0) !== DateHelper.format(dateBounds[1], format0);
        let ret = DateHelper.format(dateBounds[0], format0);
        if (end) {
          ret = DateHelper.formatRange(
            dateBounds,
            pickFormat(descriptionFormat, 1, `S${dateFormat}${me.dateSeparator}E${dateFormat}`)
          );
        }
        return ret;
      }
    }
    get rangeDescription() {
      const me = this, {
        date: date2,
        startDate,
        endDate,
        range
      } = me, month2 = me.month || (me.month = new Month({ date: date2 }));
      let rangeDesc;
      switch (range.unit) {
        case "day":
          rangeDesc = DateHelper.format(date2, "L");
          break;
        case "week": {
          const startMonth = startDate.getMonth(), endMonth = endDate.getMonth(), monthDesc = startMonth === endMonth ? DateHelper.format(startDate, "MMMM") : `${DateHelper.format(startDate, "MMM")} - ${DateHelper.format(endDate, "MMM")}`, week = month2.getWeekNumber(startDate);
          rangeDesc = `${me.L("L{Object.Week}")} ${week[1]}, ${monthDesc} ${week[0]}`;
          break;
        }
        case "month":
          rangeDesc = DateHelper.format(date2, "MMMM, YYYY");
          break;
        case "year":
          rangeDesc = DateHelper.format(date2, "YYYY");
          break;
        case "decade":
          rangeDesc = `${DateHelper.format(startDate, "DC")}`;
      }
      return rangeDesc;
    }
    changeDescriptionFormat(format2) {
      return arrayify(format2);
    }
    get widgetClass() {
    }
    // no b-describable class
  }, __publicField(_a5, "$name", "Describable"), __publicField(_a5, "configurable", {
    /**
     * A {@link Core.helper.DateHelper} format string to use to create date output for view descriptions.
     * @prp {String}
     * @default
     */
    dateFormat: "MMMM d, YYYY",
    /**
     * A string used to separate start and end dates in the {@link #config-descriptionFormat}.
     * @prp {String}
     * @default
     */
    dateSeparator: " - ",
    /**
     * The date format used by the default {@link #config-descriptionRenderer} for rendering the view's description.
     * If this value is `null`, the {@link #config-dateFormat} (and potentially {@link #config-dateSeparator}) will
     * be used.
     *
     * For views that can span a range of dates, this can be a 2-item array with the following interpretation:
     *
     * - `descriptionFormat[0]` is either a date format string or `true` (to use {@link #config-dateFormat}). The
     *   result of formatting the `startDate` with this format specification is used when the formatting both the
     *   `startDate` and `endDate` with this specification produces the same result. For example, a week view
     *   displays only the month and year components of the date, so this will be used unless the end of the week
     *   crosses into the next month.
     *
     * - `descriptionFormat[1]` is used with {@link Core.helper.DateHelper#function-formatRange-static} when the
     *  `startDate` and `endDate` format differently using `descriptionFormat[0]` (as described above). This one
     *  format string produces a result for both dates. If this value is `true`, the {@link #config-dateFormat} and
     *  {@link #config-dateSeparator} are combined to produce the range format.
     *
     * @prp {String|String[]|Boolean[]}
     * @default
     */
    descriptionFormat: null,
    /**
     * A function that provides the textual description for this view. If provided, this function overrides the
     * {@link #config-descriptionFormat}.
     *
     * ```javascript
     *  descriptionRenderer() {
     *      const
     *          eventsInView = this.eventStore.records.filter(
     *              eventRec => DateHelper.intersectSpans(
     *                  this.startDate, this.endDate,
     *                  eventRec.startDate, eventRec.endDate)).length,
     *          sd = DateHelper.format(this.startDate, 'DD/MM/YYY'),
     *          ed = DateHelper.format(this.endDate, 'DD/MM/YYY');
     *
     *     return `${sd} - ${ed}, ${eventsInView} event${eventsInView === 1 ? '' : 's'}`;
     * }
     * ```
     * @config {Function} descriptionRenderer
     * @param {Core.widget.Widget} view The active view in case the function is in another scope.
     * @returns {String} Description string
     */
    descriptionRenderer: null
  }), _a5;
};

// ../Scheduler/lib/Scheduler/view/mixin/SchedulerDom.js
var SchedulerDom_default = (Target) => {
  var _b2;
  return _b2 = class extends (Target || Base) {
    //region Get
    /**
     * Returns a single HTMLElement representing an event record assigned to a specific resource.
     * @param {Scheduler.model.AssignmentModel} assignmentRecord An assignment record
     * @returns {HTMLElement} The element representing the event record
     * @category DOM
     */
    getElementFromAssignmentRecord(assignmentRecord, returnWrapper = false, checkReleased = false, includeSplits = false) {
      var _a5, _b3, _c2, _d, _e, _f, _g;
      const me = this;
      if (me.isPainted && assignmentRecord) {
        const { foregroundCanvas } = me;
        let wrapper = (_a5 = foregroundCanvas.syncIdMap) == null ? void 0 : _a5[assignmentRecord.id];
        if (!wrapper) {
          const otherSplitView = includeSplits && (((_b3 = me.subViews) == null ? void 0 : _b3[0]) || me.splitFrom);
          if (otherSplitView) {
            wrapper = otherSplitView.getElementFromAssignmentRecord(assignmentRecord, true, checkReleased);
          }
          if (!wrapper && ((_c2 = assignmentRecord.resource) == null ? void 0 : _c2.hasLinks)) {
            for (const link of assignmentRecord.resource.$links) {
              wrapper = (_d = foregroundCanvas.syncIdMap) == null ? void 0 : _d[`${assignmentRecord.id}_${link.id}`];
              if (!wrapper) {
                if (checkReleased) {
                  wrapper = (_e = foregroundCanvas.releasedIdMap) == null ? void 0 : _e[`${assignmentRecord.id}_${link.id}`];
                  if (wrapper) {
                    break;
                  }
                }
              } else {
                break;
              }
            }
          }
        }
        if (!wrapper && checkReleased) {
          wrapper = (_f = foregroundCanvas.releasedIdMap) == null ? void 0 : _f[assignmentRecord.id];
        }
        return returnWrapper ? wrapper : (_g = wrapper == null ? void 0 : wrapper.syncIdMap) == null ? void 0 : _g.event;
      }
      return null;
    }
    /**
     * Returns a single HTMLElement representing an event record assigned to a specific resource.
     * @param {Scheduler.model.EventModel} eventRecord An event record
     * @param {Scheduler.model.ResourceModel} [resourceRecord] A resource record
     * @returns {HTMLElement} The element representing the event record
     * @category DOM
     */
    getElementFromEventRecord(eventRecord, resourceRecord = ((_a5) => (_a5 = eventRecord.resources) == null ? void 0 : _a5[0])(), returnWrapper = false, checkReleased = false) {
      var _a6;
      if (eventRecord.isResourceTimeRange) {
        const wrapper = (_a6 = this.foregroundCanvas.syncIdMap) == null ? void 0 : _a6[eventRecord.domId];
        return returnWrapper ? wrapper : wrapper == null ? void 0 : wrapper.syncIdMap.event;
      }
      const assignmentRecord = this.assignmentStore.getAssignmentForEventAndResource(eventRecord, resourceRecord);
      return this.getElementFromAssignmentRecord(assignmentRecord, returnWrapper, checkReleased);
    }
    /**
     * Returns all the HTMLElements representing an event record.
     *
     * @param {Scheduler.model.EventModel} eventRecord An event record
     * @param {Scheduler.model.ResourceModel} [resourceRecord] A resource record
     *
     * @returns {HTMLElement[]} The element(s) representing the event record
     * @category DOM
     */
    getElementsFromEventRecord(eventRecord, resourceRecord, returnWrapper = false, checkReleased = false) {
      if (resourceRecord) {
        return [this.getElementFromEventRecord(eventRecord, resourceRecord, returnWrapper, checkReleased)];
      } else {
        return eventRecord.resources.reduce((result, resourceRecord2) => {
          const el = this.getElementFromEventRecord(eventRecord, resourceRecord2, returnWrapper, checkReleased);
          el && result.push(el);
          return result;
        }, []);
      }
    }
    //endregion
    //region Resolve
    /**
     * Resolves the resource based on a dom element or event. In vertical mode, if resolving from an element higher up in
     * the hierarchy than event elements, then it is required to supply coordinates since resources are virtual
     * columns.
     * @param {HTMLElement|Event} elementOrEvent The HTML element or DOM event to resolve a resource from
     * @param {Number[]} [xy] X and Y coordinates, required in some cases in vertical mode, disregarded in horizontal
     * @returns {Scheduler.model.ResourceModel} The resource corresponding to the element, or `null` if not found.
     * @category DOM
     */
    resolveResourceRecord(elementOrEvent, xy2) {
      var _a5;
      const element = elementOrEvent instanceof Event ? elementOrEvent.target : elementOrEvent, resourceId = (_a5 = element.dataset) == null ? void 0 : _a5.resourceId, rowRecord = resourceId && this.resourceStore.getById(resourceId) || this.currentOrientation.resolveRowRecord(elementOrEvent, xy2);
      if (rowRecord == null ? void 0 : rowRecord.isSpecialRow) {
        return null;
      }
      return rowRecord;
    }
    /**
     * Product agnostic method which yields the {@link Scheduler.model.ResourceModel} record which underpins the row which
     * encapsulates the passed element. The element can be a grid cell, or an event element, and the result
     * will be a {@link Scheduler.model.ResourceModel}
     * @param {HTMLElement|Event} elementOrEvent The HTML element or DOM event to resolve a record from
     * @returns {Scheduler.model.ResourceModel} The resource corresponding to the element, or null if not found.
     * @category DOM
     */
    resolveRowRecord(elementOrEvent) {
      return this.resolveResourceRecord(elementOrEvent);
    }
    /**
     * Returns the event record for a DOM element
     * @param {HTMLElement|Event} elementOrEvent The DOM node to lookup
     * @returns {Scheduler.model.EventModel} The event record
     * @category DOM
     */
    resolveEventRecord(elementOrEvent) {
      if (elementOrEvent instanceof Event) {
        elementOrEvent = elementOrEvent.target;
      }
      const element = elementOrEvent == null ? void 0 : elementOrEvent.closest(this.eventSelector);
      if (element) {
        if ("eventId" in element.dataset) {
          return this.eventStore.getById(element.dataset.eventId);
        }
        if ("assignmentId" in element.dataset) {
          return this.assignmentStore.getById(element.dataset.assignmentId).event;
        }
      }
      return null;
    }
    // Used by shared features to resolve an event or task
    resolveTimeSpanRecord(element) {
      return this.resolveEventRecord(element);
    }
    /**
     * Returns an assignment record for a DOM element
     * @param {HTMLElement} element The DOM node to lookup
     * @privateparam {Boolean} allowReleased Whether to allow resolving from a released event
     * @returns {Scheduler.model.AssignmentModel} The assignment record
     * @category DOM
     */
    resolveAssignmentRecord(element, allowReleased = false) {
      const eventElement = element.closest(allowReleased ? "[data-assignment-id][data-event-id]" : this.eventSelector), assignmentRecord = eventElement && this.assignmentStore.getById(eventElement.dataset.assignmentId), eventRecord = eventElement && this.eventStore.getById(eventElement.dataset.eventId);
      return this.assignmentStore.getOccurrence(assignmentRecord, eventRecord);
    }
    //endregion
    // Decide if a record is inside a collapsed tree node, or inside a collapsed group (using grouping feature)
    isRowVisible(resourceRecord) {
      return this.store.indexOf(resourceRecord) >= 0;
    }
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
    isEventElement(domConfig) {
      const className = domConfig == null ? void 0 : domConfig.className;
      return Boolean(className == null ? void 0 : className[this.eventCls + "-wrap"]);
    }
  }, __publicField(_b2, "$name", "SchedulerDom"), _b2;
};

// ../Scheduler/lib/Scheduler/view/mixin/SchedulerDomEvents.js
var SchedulerDomEvents_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    //region Events
    /**
     * Triggered when user mousedowns over an empty area in the schedule.
     * @event scheduleMouseDown
     * @param {Scheduler.view.Scheduler} source This Scheduler
     * @param {Date} date Date at mouse position
     * @param {Date} tickStartDate The start date of the current time axis tick
     * @param {Date} tickEndDate The end date of the current time axis tick
     * @param {Grid.row.Row} row Row under the mouse (in horizontal mode only)
     * @param {Number} index Resource index
     * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
     * @param {MouseEvent} event Browser event
     */
    /**
     * Triggered when mouse enters an empty area in the schedule.
     * @event scheduleMouseEnter
     * @param {Scheduler.view.Scheduler} source This Scheduler
     * @param {Date} date Date at mouse position
     * @param {Date} tickStartDate The start date of the current time axis tick
     * @param {Date} tickEndDate The end date of the current time axis tick
     * @param {Grid.row.Row} row Row under the mouse (in horizontal mode only)
     * @param {Number} index Resource index
     * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
     * @param {MouseEvent} event Browser event
     */
    /**
     * Triggered when mouse leaves an empty area in the schedule.
     * @event scheduleMouseLeave
     * @param {Scheduler.view.Scheduler} source This Scheduler
     * @param {MouseEvent} event Browser event
     */
    /**
     * Triggered when user mouseups over an empty area in the schedule.
     * @event scheduleMouseUp
     * @param {Scheduler.view.Scheduler} source This Scheduler
     * @param {Date} date Date at mouse position
     * @param {Date} tickStartDate The start date of the current time axis tick
     * @param {Date} tickEndDate The end date of the current time axis tick
     * @param {Grid.row.Row} row Row under the mouse (in horizontal mode only)
     * @param {Number} index Resource index
     * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
     * @param {MouseEvent} event Browser event
     */
    /**
     * Triggered when user moves mouse over an empty area in the schedule.
     * @event scheduleMouseMove
     * @param {Scheduler.view.Scheduler} source This Scheduler
     * @param {Date} date Date at mouse position
     * @param {Scheduler.model.TimeSpan} tick A record which encapsulates the time axis tick clicked on.
     * @param {Number} tickIndex The index of the time axis tick clicked on.
     * @param {Date} tickStartDate The start date of the current time axis tick
     * @param {Date} tickEndDate The end date of the current time axis tick
     * @param {Grid.row.Row} row Row under the mouse (in horizontal mode only)
     * @param {Number} index Resource index
     * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
     * @param {MouseEvent} event Browser event
     */
    /**
     * Triggered when user clicks an empty area in the schedule.
     * @event scheduleClick
     * @param {Scheduler.view.Scheduler} source This Scheduler
     * @param {Date} date Date at mouse position
     * @param {Scheduler.model.TimeSpan} tick A record which encapsulates the time axis tick clicked on.
     * @param {Number} tickIndex The index of the time axis tick clicked on.
     * @param {Date} tickStartDate The start date of the current time axis tick
     * @param {Date} tickEndDate The end date of the current time axis tick
     * @param {Grid.row.Row} row Row under the mouse (in horizontal mode only)
     * @param {Number} index Resource index
     * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
     * @param {MouseEvent} event Browser event
     */
    /**
     * Triggered when user double-clicks an empty area in the schedule.
     * @event scheduleDblClick
     * @param {Scheduler.view.Scheduler} source This Scheduler
     * @param {Date} date Date at mouse position
     * @param {Scheduler.model.TimeSpan} tick A record which encapsulates the time axis tick clicked on.
     * @param {Number} tickIndex The index of the time axis tick clicked on.
     * @param {Date} tickStartDate The start date of the current time axis tick
     * @param {Date} tickEndDate The end date of the current time axis tick
     * @param {Grid.row.Row} row Row under the mouse (in horizontal mode only)
     * @param {Number} index Index of double-clicked resource
     * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
     * @param {MouseEvent} event Browser event
     */
    /**
     * Triggered when user right-clicks an empty area in the schedule.
     * @event scheduleContextMenu
     * @param {Scheduler.view.Scheduler} source This Scheduler
     * @param {Date} date Date at mouse position
     * @param {Scheduler.model.TimeSpan} tick A record which encapsulates the time axis tick clicked on.
     * @param {Number} tickIndex The index of the time axis tick clicked on.
     * @param {Date} tickStartDate The start date of the current time axis tick
     * @param {Date} tickEndDate The end date of the current time axis tick
     * @param {Grid.row.Row} row Row under the mouse (in horizontal mode only)
     * @param {Number} index Resource index
     * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
     * @param {MouseEvent} event Browser event
     */
    /**
     * Triggered for mouse down on an event.
     * @event eventMouseDown
     * @param {Scheduler.view.Scheduler} source This Scheduler
     * @param {Scheduler.model.EventModel} eventRecord Event record
     * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record
     * @param {Date} date Date at mouse position
     * @param {Scheduler.model.TimeSpan} tick A record which encapsulates the time axis tick clicked on.
     * @param {Number} tickIndex The index of the time axis tick clicked on.
     * @param {Date} tickStartDate The start date of the current time axis tick
     * @param {Date} tickEndDate The end date of the current time axis tick
     * @param {Grid.row.Row} row Row under the mouse (in horizontal mode only)
     * @param {Number} index Resource index
     * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
     * @param {MouseEvent} event Browser event
     */
    /**
     * Triggered for mouse up on an event.
     * @event eventMouseUp
     * @param {Scheduler.view.Scheduler} source This Scheduler
     * @param {Scheduler.model.EventModel} eventRecord Event record
     * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record
     * @param {Date} date Date at mouse position
     * @param {Scheduler.model.TimeSpan} tick A record which encapsulates the time axis tick clicked on.
     * @param {Number} tickIndex The index of the time axis tick clicked on.
     * @param {Date} tickStartDate The start date of the current time axis tick
     * @param {Date} tickEndDate The end date of the current time axis tick
     * @param {Grid.row.Row} row Row under the mouse (in horizontal mode only)
     * @param {Number} index Resource index
     * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
     * @param {MouseEvent} event Browser event
     */
    /**
     * Triggered for click on an event.
     * @event eventClick
     * @param {Scheduler.view.Scheduler} source This Scheduler
     * @param {Scheduler.model.EventModel} eventRecord Event record
     * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record
     * @param {Date} date Date at mouse position
     * @param {Scheduler.model.TimeSpan} tick A record which encapsulates the time axis tick clicked on.
     * @param {Number} tickIndex The index of the time axis tick clicked on.
     * @param {Date} tickStartDate The start date of the current time axis tick
     * @param {Date} tickEndDate The end date of the current time axis tick
     * @param {Grid.row.Row} row Row under the mouse (in horizontal mode only)
     * @param {Number} index Resource index
     * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
     * @param {MouseEvent} event Browser event
     */
    /**
     * Triggered for double-click on an event.
     * @event eventDblClick
     * @param {Scheduler.view.Scheduler} source This Scheduler
     * @param {Scheduler.model.EventModel} eventRecord Event record
     * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record
     * @param {Date} date Date at mouse position
     * @param {Scheduler.model.TimeSpan} tick A record which encapsulates the time axis tick clicked on.
     * @param {Number} tickIndex The index of the time axis tick clicked on.
     * @param {Date} tickStartDate The start date of the current time axis tick
     * @param {Date} tickEndDate The end date of the current time axis tick
     * @param {Grid.row.Row} row Row under the mouse (in horizontal mode only)
     * @param {Number} index Resource index
     * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
     * @param {MouseEvent} event Browser event
     */
    /**
     * Triggered for right-click on an event.
     * @event eventContextMenu
     * @param {Scheduler.view.Scheduler} source This Scheduler
     * @param {Scheduler.model.EventModel} eventRecord Event record
     * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record
     * @param {Date} date Date at mouse position
     * @param {Scheduler.model.TimeSpan} tick A record which encapsulates the time axis tick clicked on.
     * @param {Number} tickIndex The index of the time axis tick clicked on.
     * @param {Date} tickStartDate The start date of the current time axis tick
     * @param {Date} tickEndDate The end date of the current time axis tick
     * @param {Grid.row.Row} row Row under the mouse (in horizontal mode only)
     * @param {Number} index Resource index
     * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
     * @param {MouseEvent} event Browser event
     */
    /**
     * Triggered when the mouse enters an event bar.
     * @event eventMouseEnter
     * @param {Scheduler.view.Scheduler} source This Scheduler
     * @param {Scheduler.model.EventModel} eventRecord Event record
     * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record
     * @param {Date} date Date at mouse position
     * @param {Scheduler.model.TimeSpan} tick A record which encapsulates the time axis tick clicked on.
     * @param {Number} tickIndex The index of the time axis tick clicked on.
     * @param {Date} tickStartDate The start date of the current time axis tick
     * @param {Date} tickEndDate The end date of the current time axis tick
     * @param {Grid.row.Row} row Row under the mouse (in horizontal mode only)
     * @param {Number} index Resource index
     * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
     * @param {MouseEvent} event Browser event
     */
    /**
     * Triggered when the mouse leaves an event bar.
     * @event eventMouseLeave
     * @param {Scheduler.view.Scheduler} source This Scheduler
     * @param {Scheduler.model.EventModel} eventRecord Event record
     * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record
     * @param {Date} date Date at mouse position
     * @param {Scheduler.model.TimeSpan} tick A record which encapsulates the time axis tick clicked on.
     * @param {Number} tickIndex The index of the time axis tick clicked on.
     * @param {Date} tickStartDate The start date of the current time axis tick
     * @param {Date} tickEndDate The end date of the current time axis tick
     * @param {Grid.row.Row} row Row under the mouse (in horizontal mode only)
     * @param {Number} index Resource index
     * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
     * @param {MouseEvent} event Browser event
     */
    /**
     * Triggered for mouse over events when moving into and within an event bar.
     *
     * Note that `mouseover` events bubble, therefore this event will fire while moving from
     * element to element *within* an event bar.
     *
     * _If only an event when moving into the event bar is required, use the {@link #event-eventMouseEnter} event._
     * @event eventMouseOver
     * @param {Scheduler.view.Scheduler} source This Scheduler
     * @param {Scheduler.model.EventModel} eventRecord Event record
     * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record
     * @param {Date} date Date at mouse position
     * @param {Scheduler.model.TimeSpan} tick A record which encapsulates the time axis tick clicked on.
     * @param {Number} tickIndex The index of the time axis tick clicked on.
     * @param {Date} tickStartDate The start date of the current time axis tick
     * @param {Date} tickEndDate The end date of the current time axis tick
     * @param {Grid.row.Row} row Row under the mouse (in horizontal mode only)
     * @param {Number} index Resource index
     * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
     * @param {MouseEvent} event Browser event
     */
    /**
     * Triggered for mouse out events within and when moving out of an event bar.
     *
     * Note that `mouseout` events bubble, therefore this event will fire while moving from
     * element to element *within* an event bar.
     *
     * _If only an event when moving out of the event bar is required, use the {@link #event-eventMouseLeave} event._
     * @event eventMouseOut
     * @param {Scheduler.view.Scheduler} source This Scheduler
     * @param {Scheduler.model.EventModel} eventRecord Event record
     * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record
     * @param {Date} date Date at mouse position
     * @param {Scheduler.model.TimeSpan} tick A record which encapsulates the time axis tick clicked on.
     * @param {Number} tickIndex The index of the time axis tick clicked on.
     * @param {Date} tickStartDate The start date of the current time axis tick
     * @param {Date} tickEndDate The end date of the current time axis tick
     * @param {Grid.row.Row} row Row under the mouse (in horizontal mode only)
     * @param {Number} index Resource index
     * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
     * @param {MouseEvent} event Browser event
     */
    //endregion
    //region Event handling
    getTimeSpanMouseEventParams(eventElement, event) {
      const eventRecord = this.resolveEventRecord(eventElement);
      return eventRecord && {
        eventRecord,
        resourceRecord: this.resolveResourceRecord(eventElement),
        assignmentRecord: this.resolveAssignmentRecord(eventElement),
        eventElement,
        event
      };
    }
    getScheduleMouseEventParams(cellData, event) {
      const resourceRecord = this.isVertical ? this.resolveResourceRecord(event) : this.store.getById(cellData.id);
      return resourceRecord ? { resourceRecord } : null;
    }
    /**
     * Relays keydown events as eventkeydown if we have a selected task.
     * @private
     */
    onElementKeyDown(event) {
      const result = super.onElementKeyDown(event), me = this;
      if (me.selectedEvents.length && event.target.closest(".b-sch-event-wrap")) {
        me.trigger(me.scheduledEventName + "KeyDown", {
          eventRecords: me.selectedEvents,
          assignmentRecords: me.selectedAssignments,
          event
        });
      }
      return result;
    }
    /**
     * Relays keyup events as eventkeyup if we have a selected task.
     * @private
     */
    onElementKeyUp(event) {
      super.onElementKeyUp(event);
      const me = this;
      if (me.selectedEvents.length) {
        me.trigger(me.scheduledEventName + "KeyUp", {
          eventRecords: me.selectedEvents,
          assignmentRecords: me.selectedAssignments,
          event
        });
      }
    }
    //endregion
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "SchedulerDomEvents"), _a5;
};

// ../Scheduler/lib/Scheduler/eventlayout/HorizontalLayout.js
var HorizontalLayout = class extends Base {
  static get defaultConfig() {
    return {
      nbrOfBandsByResource: {},
      bandIndexToPxConvertFn: null,
      bandIndexToPxConvertThisObj: null
    };
  }
  clearCache(resource) {
    if (resource) {
      delete this.nbrOfBandsByResource[resource.id];
    } else {
      this.nbrOfBandsByResource = {};
    }
  }
  /**
   * This method performs layout on an array of event render data and returns amount of _bands_. Band is a multiplier of a
   * configured {@link Scheduler.view.Scheduler#config-rowHeight} to calculate total row height required to fit all
   * events.
   * This method should not be used directly, it is called by the Scheduler during the row rendering process.
   * @param {EventRenderData[]} events Unordered array of event render data, sorting may be required
   * @param {Scheduler.model.ResourceModel} resource The resource for which the events are being laid out.
   * @returns {Number}
   */
  applyLayout(events, resource) {
    return this.nbrOfBandsByResource[resource.id] = this.layoutEventsInBands(events, resource);
  }
  /**
   * This method iterates over events and calculates top position for each of them. Default layouts calculate
   * positions to avoid events overlapping horizontally (except for the 'none' layout). Pack layout will squeeze events to a single
   * row by reducing their height, Stack layout will increase the row height and keep event height intact.
   * This method should not be used directly, it is called by the Scheduler during the row rendering process.
   * @param {EventRenderData[]} events Unordered array of event render data, sorting may be required
   * @param {Scheduler.model.ResourceModel} resource The resource for which the events are being laid out.
   */
  layoutEventsInBands(events, resource) {
    throw new Error("Implement in subclass");
  }
};
HorizontalLayout._$name = "HorizontalLayout";

// ../Scheduler/lib/Scheduler/eventlayout/HorizontalLayoutStack.js
var HorizontalLayoutStack = class extends HorizontalLayout {
  static get configurable() {
    return {
      type: "stack"
    };
  }
  // Input: Array of event layout data
  // heightRun is used when pre-calculating row heights, taking a cheaper path
  layoutEventsInBands(events, resource, heightRun = false) {
    let verticalPosition = 0;
    do {
      let eventIndex = 0, event = events[0];
      while (event) {
        if (!heightRun) {
          event.top = this.bandIndexToPxConvertFn.call(
            this.bandIndexToPxConvertThisObj || this,
            verticalPosition,
            event.eventRecord,
            event.resourceRecord
          );
        }
        events.splice(eventIndex, 1);
        eventIndex = this.findClosestSuccessor(event, events);
        event = events[eventIndex];
      }
      verticalPosition++;
    } while (events.length > 0);
    return verticalPosition;
  }
  findClosestSuccessor(eventRenderData, events) {
    var _a5, _b2;
    const { endMS, group } = eventRenderData, isMilestone = ((_a5 = eventRenderData.eventRecord) == null ? void 0 : _a5.duration) === 0;
    let minGap = Infinity, closest, gap, event, eventIsMilestone;
    for (let i = 0, l = events.length; i < l; i++) {
      event = events[i];
      gap = event.startMS - endMS;
      eventIsMilestone = event.endMS - event.startMS === 0 && !((_b2 = event.eventRecord) == null ? void 0 : _b2.meta.isDragCreating);
      if (gap >= 0 && gap < minGap && // Two milestones should not overlap
      (gap > 0 || !eventIsMilestone || !isMilestone)) {
        if (this.grouped && group !== event.group) {
          break;
        }
        closest = i;
        minGap = gap;
      }
    }
    return closest;
  }
};
__publicField(HorizontalLayoutStack, "$name", "HorizontalLayoutStack");
HorizontalLayoutStack._$name = "HorizontalLayoutStack";

// ../Scheduler/lib/Scheduler/eventlayout/PackMixin.js
var rangesIntersect = (range1Start, range1End, range2Start, range2End) => {
  return range2Start <= range1Start && range1Start < range2End || range1Start <= range2Start && range2Start < range1End;
};
var PackMixin_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    isSameGroup(a, b) {
      return this.grouped ? a.group === b.group : true;
    }
    // Packs the events to consume as little space as possible
    packEventsInBands(events, applyClusterFn) {
      const me = this, { coordProp, sizeProp } = me;
      let slot, firstInCluster, cluster, j;
      for (let i = 0, l = events.length; i < l; i++) {
        firstInCluster = events[i];
        slot = me.findStartSlot(events, firstInCluster);
        cluster = me.getCluster(events, i);
        if (cluster.length > 1) {
          firstInCluster[coordProp] = slot.start;
          firstInCluster[sizeProp] = slot.end - slot.start;
          j = 1;
          while (j < cluster.length - 1 && cluster[j + 1].start - firstInCluster.start === 0) {
            j++;
          }
          const nextSlot = me.findStartSlot(events, cluster[j]);
          if (nextSlot && nextSlot.start < 0.8) {
            cluster.length = j;
          }
        }
        const clusterSize = cluster.length, slotSize = (slot.end - slot.start) / clusterSize;
        for (j = 0; j < clusterSize; j++) {
          applyClusterFn(cluster[j], j, slot, slotSize);
        }
        i += clusterSize - 1;
      }
      return 1;
    }
    findStartSlot(events, event) {
      const {
        inBandSizeProp,
        inBandCoordProp,
        coordProp,
        sizeProp
      } = this, priorOverlappers = this.getPriorOverlappingEvents(events, event);
      let i;
      if (priorOverlappers.length === 0) {
        return {
          start: 0,
          end: 1
        };
      }
      for (i = 0; i < priorOverlappers.length; i++) {
        const item = priorOverlappers[i], COORD_PROP = inBandCoordProp in item ? inBandCoordProp : coordProp, SIZE_PROP = inBandSizeProp in item ? inBandSizeProp : sizeProp;
        if (i === 0 && item[COORD_PROP] > 0) {
          return {
            start: 0,
            end: item[COORD_PROP]
          };
        } else {
          if (item[COORD_PROP] + item[SIZE_PROP] < (i < priorOverlappers.length - 1 ? priorOverlappers[i + 1][COORD_PROP] : 1)) {
            return {
              start: item[COORD_PROP] + item[SIZE_PROP],
              end: i < priorOverlappers.length - 1 ? priorOverlappers[i + 1][COORD_PROP] : 1
            };
          }
        }
      }
      return false;
    }
    getPriorOverlappingEvents(events, event) {
      const { startMS, endMS } = event, overlappers = [];
      for (let i = 0, l = events.indexOf(event); i < l; i++) {
        const item = events[i];
        if (this.isSameGroup(item, event) && rangesIntersect(startMS, endMS, item.startMS, item.endMS)) {
          overlappers.push(item);
        }
      }
      overlappers.sort(this.sortOverlappers.bind(this));
      return overlappers;
    }
    sortOverlappers(e13, e22) {
      const { coordProp } = this;
      return e13[coordProp] - e22[coordProp];
    }
    getCluster(events, startIndex) {
      const startEvent = events[startIndex], result = [startEvent];
      if (startIndex >= events.length - 1) {
        return result;
      }
      let { startMS, endMS } = startEvent;
      for (let i = startIndex + 1, l = events.length; i < l; i++) {
        const item = events[i];
        if (!this.isSameGroup(item, startEvent) || !rangesIntersect(startMS, endMS, item.startMS, item.endMS)) {
          break;
        }
        result.push(item);
        startMS = Math.max(startMS, item.startMS);
        endMS = Math.min(item.endMS, endMS);
      }
      return result;
    }
  }, __publicField(_a5, "$name", "PackMixin"), __publicField(_a5, "configurable", {
    coordProp: "top",
    sizeProp: "height",
    inBandCoordProp: "inBandTop",
    inBandSizeProp: "inBandHeight"
  }), _a5;
};

// ../Scheduler/lib/Scheduler/eventlayout/HorizontalLayoutPack.js
var HorizontalLayoutPack = class extends HorizontalLayout.mixin(PackMixin_default) {
  // Packs the events to consume as little space as possible
  layoutEventsInBands(events) {
    const result = this.packEventsInBands(events, (event, j, slot, slotSize) => {
      event.height = slotSize;
      event.top = slot.start + j * slotSize;
    });
    events.forEach((event) => {
      Object.assign(
        event,
        this.bandIndexToPxConvertFn.call(
          this.bandIndexToPxConvertThisObj || this,
          event.top,
          event.height,
          event.eventRecord,
          event.resourceRecord
        )
      );
    });
    return result;
  }
};
__publicField(HorizontalLayoutPack, "$name", "HorizontalLayoutPack");
__publicField(HorizontalLayoutPack, "configurable", {
  type: "pack"
});
HorizontalLayoutPack._$name = "HorizontalLayoutPack";

// ../Scheduler/lib/Scheduler/view/mixin/SchedulerResourceRendering.js
var SchedulerResourceRendering_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    //endregion
    //region Resource header/columns
    // NOTE: The configs below are initially applied to the resource header in `TimeAxisColumn#set mode`
    /**
     * Use it to manipulate resource column properties at runtime.
     * @property {Scheduler.view.ResourceHeader}
     * @readonly
     */
    get resourceColumns() {
      var _a6;
      return ((_a6 = this.timeAxisColumn) == null ? void 0 : _a6.resourceColumns) || this._resourceColumns;
    }
    /**
     * Get resource column width. Only applies to vertical mode. To set it, assign to
     * `scheduler.resourceColumns.columnWidth`.
     * @property {Number}
     * @readonly
     */
    get resourceColumnWidth() {
      var _a6;
      return ((_a6 = this.resourceColumns) == null ? void 0 : _a6.columnWidth) || null;
    }
    //endregion
    //region Event rendering
    // Returns a resource specific resourceMargin, falling back to Schedulers setting
    // This fn could be made public to allow hooking it as an alternative to only setting this in data
    getResourceMarginObject(resourceRecord) {
      var _a6;
      if (resourceRecord == null ? void 0 : resourceRecord.resourceMargin) {
        const margin = (_a6 = resourceRecord == null ? void 0 : resourceRecord.resourceMargin) != null ? _a6 : this.resourceMargin;
        if (typeof margin === "number") {
          return {
            start: margin,
            end: margin,
            total: margin * 2
          };
        } else {
          margin.total = margin.start + margin.end;
          return margin;
        }
      } else {
        return this.resourceMarginObject;
      }
    }
    // Returns a resource specific barMargin, falling back to Schedulers setting
    // This fn could be made public to allow hooking it as an alternative to only setting this in data
    getBarMargin(resourceRecord) {
      var _a6;
      return (_a6 = resourceRecord == null ? void 0 : resourceRecord.barMargin) != null ? _a6 : this.barMargin;
    }
    // Returns a resource specific rowHeight, falling back to Schedulers setting
    // Prio order: Height from record, configured height
    // This fn could be made public to allow hooking it as an alternative to only setting this in data
    getResourceHeight(resourceRecord) {
      var _a6;
      return (_a6 = resourceRecord.rowHeight) != null ? _a6 : this.isHorizontal ? this.rowHeight : this.getResourceWidth(resourceRecord);
    }
    getResourceWidth(resourceRecord) {
      var _a6;
      return (_a6 = resourceRecord.instanceMeta(this).columnWidth) != null ? _a6 : this.resourceColumnWidth;
    }
    // Similar to getResourceHeight(), but for usage later in the process to take height set by renderers into account.
    // Cant be used earlier in the process because then the row will grow
    // Prio order: Height requested by renderer, height from record, configured height
    getAppliedResourceHeight(resourceRecord) {
      var _a6;
      const row = this.getRowById(resourceRecord);
      return (_a6 = row == null ? void 0 : row.maxRequestedHeight) != null ? _a6 : this.getResourceHeight(resourceRecord);
    }
    // Combined convenience getter for destructuring on calling side
    // Second arg only passed for nested events, handled by NestedEvent feature
    getResourceLayoutSettings(resourceRecord, parentEventRecord = null) {
      const resourceMarginObject = this.getResourceMarginObject(resourceRecord, parentEventRecord), rowHeight = this.getAppliedResourceHeight(resourceRecord, parentEventRecord);
      return {
        barMargin: this.getBarMargin(resourceRecord, parentEventRecord),
        contentHeight: Math.max(rowHeight - resourceMarginObject.total, 1),
        rowHeight,
        resourceMarginObject
      };
    }
    //endregion
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "SchedulerResourceRendering"), //region Default config
  __publicField(_a5, "configurable", {
    /**
     * Control how much space to leave between the first event/last event and the resources edge (top/bottom
     * margin within the resource row in horizontal mode, left/right margin within the resource column in
     * vertical mode), in px. Defaults to the value of {@link Scheduler.view.Scheduler#config-barMargin}.
     *
     * Can be configured per resource by setting {@link Scheduler.model.ResourceModel#field-resourceMargin
     * resource.resourceMargin}.
     *
     * It's also possible to set different values for top/left and bottom/right
     * by assigning  an object to `resourceMargin` with `start` (margin top in horizontal mode,
     * margin left in vertical mode) and `end` (margin bottom / margin right) properties:
     * ```javascript
     * scheduler = new Scheduler({
     *     resourceMargin : {
     *         start : 15,
     *         end   : 1
     *     }
     * });
     * ```
     *
     * @prp {Number|ResourceMarginConfig}
     * @category Scheduled events
     */
    resourceMargin: null,
    /**
     * A config object used to configure the resource columns in vertical mode.
     * See {@link Scheduler.view.ResourceHeader} for more details on available properties.
     *
     * ```javascript
     * new Scheduler({
     *     resourceColumns : {
     *         columnWidth    : 100,
     *         fillWidth      : false, // this is required for columnWidth to work
     *         headerRenderer : ({ resourceRecord }) => `${resourceRecord.id} - ${resourceRecord.name}`
     *     }
     * })
     * ```
     * @config {ResourceHeaderConfig}
     * @category Resources
     */
    resourceColumns: null,
    /**
     * Path to load resource images from. Used by the resource header in vertical mode and the
     * {@link Scheduler.column.ResourceInfoColumn} in horizontal mode. Set this to display miniature
     * images for each resource using their `image` or `imageUrl` fields.
     *
     * * `image` represents image name inside the specified `resourceImagePath`,
     * * `imageUrl` represents fully qualified image URL.
     *
     *  If set and a resource has no `imageUrl` or `image` specified it will try show miniature using
     *  the resource's name with {@link #config-resourceImageExtension} appended.
     *
     * **NOTE**: The path should end with a `/`:
     *
     * ```
     * new Scheduler({
     *   resourceImagePath : 'images/resources/'
     * });
     * ```
     * @config {String}
     * @category Resources
     */
    resourceImagePath: null,
    /**
     * Generic resource image, used when provided `imageUrl` or `image` fields or path calculated from resource
     * name are all invalid. If left blank, resource name initials will be shown when no image can be loaded.
     * @default
     * @config {String}
     * @category Resources
     */
    defaultResourceImageName: null,
    /**
     * Resource image extension, used when creating image path from resource name.
     * @default
     * @config {String}
     * @category Resources
     */
    resourceImageExtension: ".jpg"
  }), _a5;
};

// ../Scheduler/lib/Scheduler/view/mixin/SchedulerEventRendering.js
var SchedulerEventRendering_default = (Target) => {
  var _a5;
  return _a5 = class extends SchedulerResourceRendering_default(Target || Base) {
    //region Default config
    static get configurable() {
      return {
        /**
         * Position of the milestone text:
         * * 'inside' - for short 1-char text displayed inside the diamond, not applicable when using
         *   {@link #config-milestoneLayoutMode})
         * * 'outside' - for longer text displayed outside the diamond, but inside it when using
         *   {@link #config-milestoneLayoutMode}
         * * 'always-outside' - outside even when combined with {@link #config-milestoneLayoutMode}
         *
         * @prp {'inside'|'outside'|'always-outside'}
         * @default
         * @category Milestones
         */
        milestoneTextPosition: "outside",
        /**
         * How to align milestones in relation to their startDate. Only applies when using a `milestoneLayoutMode`
         * other than `default`. Valid values are:
         * * start
         * * center (default)
         * * end
         * @prp {'start'|'center'|'end'}
         * @default
         * @category Milestones
         */
        milestoneAlign: "center",
        /**
         * Factor representing the average char width in pixels used to determine milestone width when configured
         * with `milestoneLayoutMode: 'estimate'`.
         * @prp {Number}
         * @default
         * @category Milestones
         */
        milestoneCharWidth: 10,
        /**
         * How to handle milestones during event layout. How the milestones are displayed when part of the layout
         * are controlled using {@link #config-milestoneTextPosition}.
         *
         * Options are:
         * * default - Milestones do not affect event layout
         * * estimate - Milestone width is estimated by multiplying text length with Scheduler#milestoneCharWidth
         * * data - Milestone width is determined by checking EventModel#milestoneWidth
         * * measure - Milestone width is determined by measuring label width
         * Please note that currently text width is always determined using EventModel#name.
         * Also note that only 'default' is supported by eventStyles line, dashed and minimal.
         * @prp {'default'|'estimate'|'data'|'measure'}
         * @default
         * @category Milestones
         */
        milestoneLayoutMode: "default",
        /**
         * Defines how to handle overlapping events. Valid values are:
         * - `stack`, adjusts row height (only horizontal)
         * - `pack`, adjusts event height in horizontal mode, (width in vertical mode) to fit all events
         * - `mixed`, allows two events to overlap, more packs (only vertical)
         * - `none`, allows events to overlap
         *
         * In horizontal mode, the default value is `stack`, in vertical mode it is `pack`.
         * This config can also accept an object:
         *
         * ```javascript
         * new Scheduler({
         *     eventLayout : { type : 'stack' }
         * })
         * ```
         *
         * @prp {'stack'|'pack'|'mixed'|'none'|Object} eventLayout
         * @property {'stack'|'pack'|'mixed'|'none'} eventLayout.type
         * @default
         * @category Scheduled events
         */
        eventLayout: "stack",
        /**
         * Override this method to provide a custom sort function to sort any overlapping events. See {@link
         * #config-overlappingEventSorter} for more details.
         *
         * @param  {Scheduler.model.EventModel} a First event
         * @param  {Scheduler.model.EventModel} b Second event
         * @returns {Number} Return -1 to display `a` above `b`, 1 for `b` above `a`
         * @member {Function} overlappingEventSorter
         * @category Misc
         */
        /**
         * Override this method to provide a custom sort function to sort any overlapping events. This only applies
         * to the horizontal mode, where the order the events are sorted in determines their vertical placement
         * within a resource.
         *
         * By default, overlapping events are laid out based on the start date. If the start date is equal, events
         * with earlier end date go first. And lastly the name of events is taken into account.
         *
         * Here's a sample sort function, sorting on start- and end date. If this function returns -1, then event
         * `a` is placed above event `b`:
         *
         * ```javascript
         * overlappingEventSorter(a, b) {
         *
         *   const startA = a.startDate, endA = a.endDate;
         *   const startB = b.startDate, endB = b.endDate;
         *
         *   const sameStart = (startA - startB === 0);
         *
         *   if (sameStart) {
         *     return endA > endB ? -1 : 1;
         *   } else {
         *     return (startA < startB) ? -1 : 1;
         *   }
         * }
         * ```
         *
         * NOTE: The algorithms (stack, pack) that lay the events out expects them to be served in chronological
         * order, be sure to first sort by `startDate` to get predictable results.
         *
         * @param  {Scheduler.model.EventModel} a First event
         * @param  {Scheduler.model.EventModel} b Second event
         * @returns {Number} Return -1 to display `a` above `b`, 1 for `b` above `a`
         * @config {Function}
         * @category Misc
         */
        overlappingEventSorter: null,
        /**
         * By default, scheduler fade events in on load. Specify `false` to prevent this animation or specify one
         * of the available animation types to use it (`true` equals `'fade-in'`):
         * * fade-in (default)
         * * slide-from-left
         * * slide-from-top
         * ```
         * // Slide events in from the left on load
         * scheduler = new Scheduler({
         *     useInitialAnimation : 'slide-from-left'
         * });
         * ```
         * @prp {Boolean|'fade-in'|'slide-from-left'|'slide-from-top'|String}
         * @default
         * @category Misc
         */
        useInitialAnimation: true,
        /**
         * An empty function by default, but provided so that you can override it. This function is called each time
         * an event is rendered into the schedule to render the contents of the event. It's called with the event,
         * its resource and a `renderData` object which allows you to populate data placeholders inside the event
         * template.
         *
         * <div class="note">
         * You should never modify any records inside this method.
         * </div>
         *
         * By default, the DOM markup of an event bar includes placeholders for 'cls' and 'style'. The cls property
         * is a {@link Core.helper.util.DomClassList} which will be added to the event element. The style property
         * is an inline style declaration for the event element.
         *
         * <div class="note">
         * When returning content, be sure to consider how that content should be encoded to avoid XSS
         * (Cross-Site Scripting) attacks. This is especially important when including user-controlled data such as
         * the event's <code>name</code>. The function {@link Core.helper.StringHelper#function-encodeHtml-static} as well as
         * {@link Core.helper.StringHelper#function-xss-static} can be helpful in these cases.
         * </div>
         *
         * ```javascript
         *  eventRenderer({ eventRecord, resourceRecord, renderData }) {
         *      renderData.style = 'color:white';                 // You can use inline styles too.
         *
         *      // Property names with truthy values are added to the resulting elements CSS class.
         *      renderData.cls.isImportant = this.isImportant(eventRecord);
         *      renderData.cls.isModified = eventRecord.isModified;
         *
         *      // Remove a class name by setting the property to false
         *      renderData.cls[scheduler.generatedIdCls] = false;
         *
         *      // Or, you can treat it as a string, but this is less efficient, especially
         *      // if your renderer wants to *remove* classes that may be there.
         *      renderData.cls += ' extra-class';
         *
         *      return StringHelper.xss`${DateHelper.format(eventRecord.startDate, 'YYYY-MM-DD')}: ${eventRecord.name}`;
         *  }
         * ```
         *
         * @param {Object} detail An object containing the information needed to render an Event.
         * @param {Scheduler.model.EventModel} detail.eventRecord The event record.
         * @param {Scheduler.model.ResourceModel} detail.resourceRecord The resource record.
         * @param {Scheduler.model.AssignmentModel} detail.assignmentRecord The assignment record.
         * @param {Scheduler.view.Scheduler} detail.scheduler The Scheduler instance.
         * @param {Object} detail.renderData An object containing details about the event rendering.
         * @param {Scheduler.model.EventModel} detail.renderData.event The event record.
         * @param {Core.helper.util.DomClassList|String} detail.renderData.cls An object whose property names
         * represent the CSS class names to be added to the event bar element. Set a property's value to truthy or
         * falsy to add or remove the class name based on the property name. Using this technique, you do not have
         * to know whether the class is already there, or deal with concatenation.
         * @param {Core.helper.util.DomClassList|String} detail.renderData.wrapperCls An object whose property names
         * represent the CSS class names to be added to the event wrapper element. Set a property's value to truthy
         * or falsy to add or remove the class name based on the property name. Using this technique, you do not
         * have to know whether the class is already there, or deal with concatenation.
         * @param {Core.helper.util.DomClassList|String} detail.renderData.iconCls An object whose property names
         * represent the CSS class names to be added to an event icon element.
         *
         * Note that an element carrying this icon class is injected into the event element *after*
         * the renderer completes, *before* the renderer's created content.
         *
         * To disable this if the renderer takes full control and creates content using the iconCls,
         * you can set `renderData.iconCls = null`.
         * @param {Number} detail.renderData.left Vertical offset position (in pixels) on the time axis.
         * @param {Number} detail.renderData.width Width in pixels of the event element.
         * @param {Number} detail.renderData.height Height in pixels of the event element.
         * @param {String|Object<String,String>} detail.renderData.style Inline styles for the event bar DOM element.
         * Use either 'border: 1px solid black' or `{ border: '1px solid black' }`
         * @param {String|Object<String,String>} detail.renderData.wrapperStyle Inline styles for wrapper of the
         * event bar DOM element. Use either 'border: 1px solid green' or `{ border: '1px solid green' }`
         * @param {String} detail.renderData.eventStyle The `eventStyle` of the event. Use this to apply custom
         * styles to the event DOM element
         * @param {String} detail.renderData.eventColor The `eventColor` of the event. Use this to set a custom
         * color for the rendered event
         * @param {String} detail.renderData.ariaLabel A description of the event details used for screen readers
         * @param {DomConfig[]} detail.renderData.children An array of DOM configs used as children to the
         * `b-sch-event` element. Can be populated with additional DOM configs to have more control over contents.
         * @returns {String|DomConfig|DomConfig[]} A simple string, or a DomConfig (or array thereof)
         * @config {Function}
         * @category Scheduled events
         */
        eventRenderer: null,
        /**
         * `this` reference for the {@link #config-eventRenderer} function
         * @config {Object}
         * @category Scheduled events
         */
        eventRendererThisObj: null,
        /**
         * Field from EventModel displayed as text in the bar when rendering
         * @config {String}
         * @default
         * @category Scheduled events
         */
        eventBarTextField: "name",
        /**
         * The class responsible for the packing horizontal event layout process.
         * Override this to take control over the layout process.
         * @config {Scheduler.eventlayout.HorizontalLayout}
         * @typings {typeof HorizontalLayout}
         * @default
         * @private
         * @category Misc
         */
        horizontalLayoutPackClass: HorizontalLayoutPack,
        /**
         * The class name responsible for the stacking horizontal event layout process.
         * Override this to take control over the layout process.
         * @config {Scheduler.eventlayout.HorizontalLayout}
         * @typings {typeof HorizontalLayout}
         * @default
         * @private
         * @category Misc
         */
        horizontalLayoutStackClass: HorizontalLayoutStack,
        /**
         * Controls how much space to leave between stacked event bars in px.
         *
         * Can be configured per resource by setting {@link Scheduler.model.ResourceModel#field-barMargin
         * resource.barMargin}.
         *
         * @config {Number} barMargin
         * @default
         * @category Scheduled events
         */
        // Used to animate events on first render
        isFirstRender: true,
        initialAnimationDuration: 2e3,
        /**
         * When an event bar has a width less than this value, it gets the CSS class `b-sch-event-narrow`
         * added. You may apply custom CSS rules using this class.
         *
         * In vertical mode, this class causes the text to be rotated so that it runs vertically.
         * @default
         * @config {Number}
         * @category Scheduled events
         */
        narrowEventWidth: 10,
        /**
         * An advanced setting that controls the maximum number of released event bars to keep in the DOM.
         *
         * Schedulers virtualization releases event bars that go out of view, which means they are hidden but not
         * removed from the DOM. When another event bar enters view, a released one is reused. This is faster than
         * removing and re-adding the event bars from the DOM.
         *
         * By default, all released event bars are kept around, and you should in general not need to adjust this
         * setting. But if you have an app in which the scheduler sometimes displays a very large number of events
         * on the screen at the same time, and sometimes only a few, you can experiment with tweaking this setting
         * to free up some memory in the sparse case.
         *
         * @prp {Number}
         */
        eventReleaseThreshold: null,
        internalEventLayout: null,
        eventPositionMode: "translate",
        eventScrollMode: "move",
        testConfig: {
          useInitialAnimation: false
        }
      };
    }
    //endregion
    //region Settings
    changeEventLayout(eventLayout) {
      if (this.isConfiguring && this.mode === "vertical" && (eventLayout === "stack" || eventLayout.type === "stack")) {
        eventLayout = "pack";
      }
      this.internalEventLayout = eventLayout;
      return this.internalEventLayout.type;
    }
    changeInternalEventLayout(eventLayout) {
      return this.getEventLayout(eventLayout);
    }
    updateInternalEventLayout(eventLayout, oldEventLayout) {
      const me = this;
      if (oldEventLayout) {
        me.element.classList.remove(`b-eventlayout-${oldEventLayout.type}`);
      }
      me.element.classList.add(`b-eventlayout-${eventLayout.type}`);
      if (eventLayout.type === "layoutFn" && me.isVertical) {
        me.resourceColumns.fitWidth = me.resourceColumns.fillWidth = false;
      }
      if (!me.isConfiguring) {
        me.refreshWithTransition();
        me.trigger("stateChange");
      }
    }
    updateOverlappingEventSorter(fn2) {
      if (!this.isConfiguring) {
        this.refreshWithTransition();
      }
    }
    updateEventReleaseThreshold() {
      if (!this.isConfiguring) {
        this.refresh();
      }
    }
    //endregion
    //region Layout helpers
    // Wraps string config to object with type
    getEventLayout(value2) {
      if (value2 == null ? void 0 : value2.isModel) {
        value2 = value2.eventLayout || this.internalEventLayout;
      }
      if (typeof value2 === "string") {
        value2 = { type: value2 };
      }
      return value2;
    }
    /**
     * Get event layout handler. The handler decides the vertical placement of events within a resource.
     * Returns null if no eventLayout is used (if {@link #config-eventLayout} is set to "none")
     * @internal
     * @returns {Scheduler.eventlayout.HorizontalLayout}
     * @readonly
     * @category Scheduled events
     */
    getEventLayoutHandler(eventLayout) {
      const me = this;
      if (!me.isHorizontal) {
        return null;
      }
      const { timeAxisViewModel, horizontal } = me, { type } = eventLayout;
      if (!me.layouts) {
        me.layouts = {};
      }
      switch (type) {
        case "stack": {
          if (!me.layouts.horizontalStack) {
            me.layouts.horizontalStack = new me.horizontalLayoutStackClass(ObjectHelper.assign({
              scheduler: me,
              timeAxisViewModel,
              bandIndexToPxConvertFn: horizontal.layoutEventVerticallyStack,
              bandIndexToPxConvertThisObj: horizontal
            }, eventLayout));
          }
          return me.layouts.horizontalStack;
        }
        case "pack": {
          if (!me.layouts.horizontalPack) {
            me.layouts.horizontalPack = new me.horizontalLayoutPackClass(ObjectHelper.assign({
              scheduler: me,
              timeAxisViewModel,
              bandIndexToPxConvertFn: horizontal.layoutEventVerticallyPack,
              bandIndexToPxConvertThisObj: horizontal
            }, eventLayout));
          }
          return me.layouts.horizontalPack;
        }
        default:
          return null;
      }
    }
    //endregion
    //region Event rendering
    // Chainable function called with the events to render for a specific resource. Allows features to add/remove.
    // Chained by ResourceTimeRanges
    getEventsToRender(resource, events) {
      return events;
    }
    /**
     * Rerenders events for specified resource (by rerendering the entire row).
     * @param {Scheduler.model.ResourceModel} resourceRecord
     * @category Rendering
     */
    repaintEventsForResource(resourceRecord) {
      this.currentOrientation.repaintEventsForResource(resourceRecord);
    }
    /**
     * Rerenders the events for all resources connected to the specified event
     * @param {Scheduler.model.EventModel} eventRecord
     * @private
     */
    repaintEvent(eventRecord) {
      const resources = this.eventStore.getResourcesForEvent(eventRecord);
      resources.forEach((resourceRecord) => this.repaintEventsForResource(resourceRecord));
    }
    getEventStyle(eventRecord, resourceRecord) {
      return eventRecord.eventStyle || resourceRecord.eventStyle || this.eventStyle;
    }
    getEventColor(eventRecord, resourceRecord) {
      var _a6, _b2;
      return eventRecord.eventColor || ((_a6 = eventRecord.event) == null ? void 0 : _a6.eventColor) || ((_b2 = eventRecord.parent) == null ? void 0 : _b2.eventColor) || resourceRecord.eventColor || this.eventColor;
    }
    //endregion
    //region Template
    /**
     * Generates data used in the template when rendering an event. For example which css classes to use. Also calls the
     * {@link #config-eventRenderer}.
     * @private
     * @param {Scheduler.model.EventModel} eventRecord Event to generate data for
     * @param {Scheduler.model.ResourceModel} resourceRecord Events resource
     * @param {Boolean|Object} includeOutside Specify true to get boxes for timespans outside the rendered zone in both
     * dimensions. This option is used when calculating dependency lines, and we need to include routes from timespans
     * which may be outside the rendered zone.
     * @param {Boolean} includeOutside.timeAxis Pass as `true` to include timespans outside the TimeAxis's bounds
     * @param {Boolean} includeOutside.viewport Pass as `true` to include timespans outside the vertical timespan viewport's bounds.
     * @returns {Object} Data to use in event template, or `undefined` if the event is outside the rendered zone.
     */
    generateRenderData(eventRecord, resourceRecord, includeOutside = { viewport: true }) {
      var _a6, _b2;
      const me = this, renderData = me.currentOrientation.getTimeSpanRenderData(eventRecord, resourceRecord, includeOutside), { isEvent } = eventRecord, { eventResize } = me.features, isMilestone = !eventRecord.meta.isDragCreating && eventRecord.isMilestone, assignmentRecord = isEvent && eventRecord.assignments.find((a) => a.resourceId === resourceRecord.$originalId), eventContent = {
        className: "b-sch-event-content",
        role: "presentation",
        dataset: {
          taskBarFeature: "content"
        }
      };
      if (renderData) {
        Object.assign(renderData, {
          // Mark as wrapper to make sure fire render events for this level only
          isWrap: true,
          wrapperStyle: "",
          tabIndex: "0",
          // Pass record generations to allow WrapperHelper to decide if portals need recreating or not
          // (it can reach them using wrap.elementData.eventGeneration etc.)
          eventGeneration: eventRecord.generation,
          resourceGeneration: resourceRecord.generation,
          assignmentGeneration: assignmentRecord == null ? void 0 : assignmentRecord.generation
        });
        let resizable = eventRecord.isResizable;
        if (eventResize && resizable) {
          if (renderData.startsOutsideView) {
            if (resizable === true) {
              resizable = "end";
            } else if (resizable === "start") {
              resizable = false;
            }
          }
          if (renderData.endsOutsideView) {
            if (resizable === true) {
              resizable = "start";
            } else if (resizable === "end") {
              resizable = false;
            }
          }
          if (resizable) {
            if (me.isHorizontal) {
              if (!me.rtl && !eventResize.leftHandle || me.rtl && !eventResize.rightHandle) {
                resizable = resizable === "start" ? false : "end";
              } else if (!me.rtl && !eventResize.rightHandle || me.rtl && !eventResize.leftHandle) {
                resizable = resizable === "end" ? false : "start";
              }
            } else {
              if (!eventResize.topHandle) {
                resizable = resizable === "start" ? false : "end";
              } else if (!eventResize.bottomHandle) {
                resizable = resizable === "end" ? false : "start";
              }
            }
          }
        }
        const isDirty = Boolean(
          eventRecord.hasPersistableChanges || (assignmentRecord == null ? void 0 : assignmentRecord.hasPersistableChanges)
        ), clsListObj = {
          [resourceRecord.cls]: resourceRecord.cls,
          [me.generatedIdCls]: !eventRecord.isOccurrence && eventRecord.hasGeneratedId,
          [me.dirtyCls]: isDirty,
          [me.committingCls]: eventRecord.isCommitting,
          [me.endsOutsideViewCls]: renderData.endsOutsideView,
          [me.startsOutsideViewCls]: renderData.startsOutsideView,
          "b-clipped-start": renderData.clippedStart,
          "b-clipped-end": renderData.clippedEnd,
          "b-iscreating": eventRecord.isCreating,
          "b-rtl": me.rtl
        }, wrapperClsListObj = {
          [`${me.eventCls}-parent`]: resourceRecord.isParent,
          "b-readonly": eventRecord.readOnly || (assignmentRecord == null ? void 0 : assignmentRecord.readOnly),
          "b-linked-resource": resourceRecord.isLinked,
          "b-original-resource": resourceRecord.hasLinks
        }, clsList = eventRecord.isResourceTimeRange ? new DomClassList() : eventRecord.internalCls.clone(), wrapperClsList = eventRecord.isResourceTimeRange ? eventRecord.internalCls.clone() : new DomClassList();
        if (isEvent) {
          const selected = assignmentRecord && me.isAssignmentSelected(assignmentRecord);
          ObjectHelper.assign(clsListObj, {
            [me.eventCls]: 1,
            "b-milestone": isMilestone,
            "b-sch-event-narrow": !isMilestone && renderData.width < me.narrowEventWidth,
            [me.fixedEventCls]: eventRecord.isDraggable === false,
            [`b-sch-event-resizable-${resizable}`]: Boolean(eventResize && !eventRecord.readOnly),
            [me.eventSelectedCls]: selected,
            [me.eventAssignHighlightCls]: me.eventAssignHighlightCls && !selected && me.isEventSelected(eventRecord),
            "b-recurring": eventRecord.isRecurring,
            "b-occurrence": eventRecord.isOccurrence,
            "b-inactive": eventRecord.inactive
          });
          renderData.eventId = eventRecord.id;
          const eventStyle2 = me.getEventStyle(eventRecord, resourceRecord), eventColor2 = me.getEventColor(eventRecord, resourceRecord), hasAnimation = me.isFirstRender && me.useInitialAnimation && globalThis.bryntum.noAnimations !== true;
          ObjectHelper.assign(wrapperClsListObj, {
            [`${me.eventCls}-wrap`]: 1,
            "b-milestone-wrap": isMilestone,
            [me.navigator.focusCls]: eventRecord.assignments.some((a) => a === me.activeAssignment)
          });
          if (hasAnimation) {
            const index = renderData.row ? renderData.row.index : (renderData.top - me.scrollTop) / me.tickSize, delayMS = index / 20 * 1e3;
            renderData.wrapperStyle = `animation-delay: ${delayMS}ms;`;
            me.maxDelay = Math.max(me.maxDelay || 0, delayMS);
            if (!me.initialAnimationDetacher) {
              me.initialAnimationDetacher = EventHelper.on({
                element: me.foregroundCanvas,
                delegate: me.eventSelector,
                // Just listen for the first animation end fired by our event els
                once: true,
                animationend: () => me.setTimeout({
                  fn: "stopInitialAnimation",
                  delay: me.maxDelay,
                  cancelOutstanding: true
                }),
                // Fallback in case animation is interrupted
                expires: {
                  alt: "stopInitialAnimation",
                  delay: me.initialAnimationDuration + me.maxDelay
                },
                thisObj: me
              });
            }
          }
          renderData.eventColor = eventColor2;
          renderData.eventStyle = eventStyle2;
          renderData.assignmentRecord = renderData.assignment = assignmentRecord;
        }
        renderData.wrapperCls = ObjectHelper.assign(wrapperClsList, wrapperClsListObj);
        renderData.cls = ObjectHelper.assign(clsList, clsListObj);
        renderData.iconCls = new DomClassList(eventRecord.getValue(me.eventBarIconClsField) || eventRecord.iconCls);
        if (eventRecord.isResourceTimeRange) {
          renderData.style = "";
          renderData.wrapperStyle += eventRecord.style || "";
        } else {
          renderData.style = eventRecord.style || "";
        }
        renderData.resource = renderData.resourceRecord = resourceRecord;
        renderData.resourceId = renderData.rowId;
        if (isEvent) {
          let childContent = null, milestoneLabelConfig = null, value2;
          if (me.eventRenderer) {
            const rendererValue = me.eventRenderer.call(me.eventRendererThisObj || me, {
              eventRecord,
              resourceRecord,
              assignmentRecord: renderData.assignmentRecord,
              scheduler: me,
              renderData
            });
            if (renderData.ariaLabel) {
              renderData.ariaLabelFromEventRenderer = true;
            }
            if (typeof renderData.cls === "string") {
              renderData.cls = new DomClassList(renderData.cls);
            }
            if (typeof renderData.wrapperCls === "string") {
              renderData.wrapperCls = new DomClassList(renderData.wrapperCls);
            }
            if (typeof renderData.iconCls === "string") {
              renderData.iconCls = new DomClassList(renderData.iconCls);
            }
            value2 = rendererValue;
          } else if (me.eventBarTextField) {
            value2 = StringHelper.encodeHtml(eventRecord.getValue(me.eventBarTextField) || "");
          }
          eventContent.children = [];
          if (isMilestone && (me.milestoneLayoutMode === "default" || me.milestoneTextPosition === "always-outside") && value2 != null && value2 !== "") {
            eventContent.children.unshift(milestoneLabelConfig = {
              tag: "label",
              children: [],
              dataset: {
                isMilestone: true
              }
            });
          }
          if ((_a6 = renderData.iconCls) == null ? void 0 : _a6.length) {
            eventContent.children.unshift({
              tag: "i",
              className: renderData.iconCls
            });
          }
          if (Array.isArray(value2)) {
            (milestoneLabelConfig || eventContent).children.push(...value2);
          } else if (StringHelper.isHtml(value2)) {
            if (eventContent.children.length) {
              childContent = {
                tag: "span",
                class: "b-event-text-wrap",
                html: value2
              };
            } else {
              eventContent.children = null;
              eventContent.html = value2;
            }
          } else if (typeof value2 === "string" || typeof value2 === "object") {
            childContent = value2;
          } else if (value2 != null) {
            childContent = String(value2);
          }
          if (childContent != null) {
            (milestoneLabelConfig || eventContent).children.push(childContent);
            renderData.cls.add("b-has-content");
          }
          if (eventContent.html != null || eventContent.children.length) {
            renderData.children.push(eventContent);
          }
          renderData.cls.toggle("b-highlighted", Boolean(assignmentRecord == null ? void 0 : assignmentRecord.instanceMeta(me).isHighlighted));
        }
        const { eventStyle, eventColor, wrapperCls } = renderData;
        wrapperCls[`b-sch-style-${eventStyle || "none"}`] = 1;
        if (DomHelper.isNamedColor(eventColor)) {
          wrapperCls[`b-sch-color-${eventColor}`] = eventColor;
        } else if (eventColor) {
          const colorProp = eventStyle ? "color" : "background-color", style = `${colorProp}:${eventColor};`;
          renderData.style = style + renderData.style;
          wrapperCls["b-sch-custom-color"] = 1;
          renderData._customColorStyle = style;
        } else {
          wrapperCls[`b-sch-color-none`] = 1;
        }
        if (renderData.style && isMilestone && eventContent) {
          eventContent.style = renderData.style;
          delete renderData.style;
        }
        renderData.cls["b-sch-event-withicon"] = (_b2 = renderData.iconCls) == null ? void 0 : _b2.length;
        renderData.eventContent = eventContent;
        renderData.wrapperChildren = [];
        if (!renderData.ariaLabelFromEventRenderer) {
          const startDateStr = DateHelper.format(eventRecord.startDate, this.displayDateFormat);
          renderData.ariaLabel = me.L("L{SchedulerBase.eventAriaLabel}")(eventRecord, startDateStr);
        }
        me.onEventDataGenerated(renderData);
      }
      return renderData;
    }
    /**
     * A method which may be chained by features. It is called when an event's render
     * data is calculated so that features may update the style, class list or body.
     * @param {Object} eventData
     * @internal
     */
    onEventDataGenerated(eventData) {
    }
    //endregion
    //region Initial animation
    changeUseInitialAnimation(name) {
      return name === true ? "fade-in" : name;
    }
    updateUseInitialAnimation(name, old) {
      const { classList } = this.element;
      if (old) {
        classList.remove(`b-initial-${old}`);
      }
      if (name) {
        classList.add(`b-initial-${name}`);
        if (BrowserHelper.isFirefox) {
          classList.add("b-prevent-event-transitions");
        }
      }
    }
    /**
     * Restarts initial events animation with new value {@link #config-useInitialAnimation}.
     * @param {Boolean|String} initialAnimation new initial animation value
     * @category Misc
     */
    restartInitialAnimation(initialAnimation) {
      var _a6;
      const me = this;
      (_a6 = me.initialAnimationDetacher) == null ? void 0 : _a6.call(me);
      me.initialAnimationDetacher = null;
      me.useInitialAnimation = initialAnimation;
      me.isFirstRender = true;
      me.refresh();
    }
    stopInitialAnimation() {
      const me = this;
      me.initialAnimationDetacher();
      me.isFirstRender = false;
      me.useInitialAnimation = false;
      if (BrowserHelper.isFirefox) {
        me.setTimeout(() => me.element.classList.remove("b-prevent-event-transitions"), 100);
      }
    }
    //endregion
    //region Milestones
    /**
     * Determines width of a milestones label. How width is determined is decided by configuring
     * {@link #config-milestoneLayoutMode}. Please note that text width is always determined using the events
     * {@link Scheduler/model/EventModel#field-name}.
     * @param {Scheduler.model.EventModel} eventRecord
     * @param {Scheduler.model.ResourceModel} resourceRecord
     * @returns {Number}
     * @category Milestones
     */
    getMilestoneLabelWidth(eventRecord, resourceRecord) {
      const me = this, mode = me.milestoneLayoutMode, size = me.getResourceLayoutSettings(resourceRecord).contentHeight;
      if (mode === "measure") {
        const html = StringHelper.encodeHtml(eventRecord.name), color = me.getEventColor(eventRecord, resourceRecord), style = me.getEventStyle(eventRecord, resourceRecord), element = me.milestoneMeasureElement || (me.milestoneMeasureElement = DomHelper.createElement({
          className: {
            "b-sch-event-wrap": 1,
            "b-milestone-wrap": 1,
            "b-measure": 1,
            [`b-sch-color-${color}`]: color,
            [`b-sch-style-${style}`]: style
          },
          children: [
            {
              className: "b-sch-event b-milestone",
              children: [
                {
                  className: "b-sch-event-content",
                  children: [
                    { tag: "label" }
                  ]
                }
              ]
            }
          ],
          parent: me.foregroundCanvas
        }));
        element.retainElement = true;
        element.style.fontSize = `${size}px`;
        if (me.milestoneTextPosition === "always-outside") {
          const label = element.firstElementChild.firstElementChild.firstElementChild;
          label.innerHTML = html;
          const bounds = Rectangle.from(label, label.parentElement);
          return bounds.left + bounds.width + 2;
        } else {
          element.firstElementChild.firstElementChild.innerHTML = `<label></label>${html}`;
          return element.firstElementChild.offsetWidth;
        }
      }
      if (mode === "estimate") {
        return eventRecord.name.length * me.milestoneCharWidth + (me.milestoneTextPosition === "always-outside" ? size : 0);
      }
      if (mode === "data") {
        return eventRecord.milestoneWidth + (me.milestoneTextPosition === "always-outside" ? size : 0);
      }
      return 0;
    }
    updateMilestoneLayoutMode(mode) {
      const me = this, alwaysOutside = me.milestoneTextPosition === "always-outside";
      me.element.classList.toggle("b-sch-layout-milestones", mode !== "default" && !alwaysOutside);
      me.element.classList.toggle("b-sch-layout-milestone-labels", mode !== "default" && alwaysOutside);
      if (!me.isConfiguring) {
        me.refreshWithTransition();
      }
    }
    updateMilestoneTextPosition(position) {
      this.element.classList.toggle("b-sch-layout-milestone-text-position-inside", position === "inside");
      this.updateMilestoneLayoutMode(this.milestoneLayoutMode);
    }
    updateMilestoneAlign() {
      if (!this.isConfiguring) {
        this.refreshWithTransition();
      }
    }
    updateMilestoneCharWidth() {
      if (!this.isConfiguring) {
        this.refreshWithTransition();
      }
    }
    // endregion
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "SchedulerEventRendering"), _a5;
};

// ../Scheduler/lib/Scheduler/data/mixin/ProjectConsumer.js
var engineStoreNames = [
  "assignmentStore",
  "dependencyStore",
  "eventStore",
  "resourceStore"
];
var ProjectConsumer_default = (Target) => {
  var _a5, _suspendedByRestore;
  return _a5 = class extends (Target || Base) {
    constructor() {
      super(...arguments);
      __privateAdd(this, _suspendedByRestore, void 0);
    }
    //region Default config
    static get declarable() {
      return ["projectStores"];
    }
    static get configurable() {
      return {
        projectModelClass: ProjectModel,
        /**
         * A {@link Scheduler.model.ProjectModel} instance or a config object. The project holds all Scheduler data.
         * Can be omitted in favor of individual store configs or {@link Scheduler.view.mixin.SchedulerStores#config-crudManager} config.
         *
         * **Note:** In SchedulerPro the project is instance of SchedulerPro.model.ProjectModel class.
         * @prp {Scheduler.model.ProjectModel|ProjectModelConfig} project
         * @typings {ProjectModel|ProjectModelConfig}
         * @category Data
         */
        project: {},
        /**
         * Configure as `true` to destroy the Project and stores when `this` is destroyed.
         * @config {Boolean}
         * @category Data
         */
        destroyStores: null,
        // Will be populated by AttachToProjectMixin which features mix in
        projectSubscribers: []
      };
    }
    //endregion
    startConfigure(config) {
      this.getConfig("project");
      super.startConfigure(config);
    }
    //region Project
    // This is where all the ingestion happens.
    // At config time, the changers inject incoming values into the project config object
    // that we are building. At the end we instantiate the project with all incoming
    // config values filled in.
    changeProject(project, oldProject) {
      const me = this, {
        projectDataNames
      } = me.constructor;
      me.projectCallbacks = /* @__PURE__ */ new Set();
      if (project) {
        me.buildingProjectConfig = true;
        if (!project.isModel) {
          if (me.isConfiguring) {
            me._project = project;
            const { crudManager } = me;
            if (crudManager) {
              this.updateStoresFromCrudManager();
            }
            me.getConfig("projectStores");
            for (const dataName of projectDataNames) {
              me.getConfig(dataName);
            }
          }
          const { eventStore } = project;
          let { _sharedProject: sharedProject } = me;
          if ((eventStore == null ? void 0 : eventStore.autoLoad) && !eventStore.isEventStoreMixin && !eventStore.count && !eventStore.lazyLoad) {
            eventStore.autoLoad = false;
            me.delayAutoLoad = true;
          }
          if (sharedProject && engineStoreNames.some((store) => project[store] && project[store] !== sharedProject[store])) {
            for (const store of engineStoreNames) {
              if (project[store] && project[store] === sharedProject[store]) {
                project[store] = project[store].chain();
              }
            }
            sharedProject = null;
          }
          project = sharedProject || new me.projectModelClass(project);
          delete me._project;
        }
        me.buildingProjectConfig = false;
      }
      return project;
    }
    updateStoresFromCrudManager() {
      const { crudManager } = this, { isCrudManager } = crudManager;
      for (const storeName of this.constructor.projectStoreNames) {
        if (crudManager[storeName]) {
          this[storeName] = crudManager[storeName];
          if (!isCrudManager) {
            delete crudManager[storeName];
          }
        }
      }
    }
    /**
     * Implement in subclass to take action when project is replaced.
     *
     * __`super.updateProject(...arguments)` must be called first.__
     *
     * @param {Scheduler.model.ProjectModel} project
     * @category Data
     */
    updateProject(project, oldProject) {
      var _a6;
      const me = this, {
        projectListeners,
        crudManager
      } = me;
      me.detachListeners("projectConsumer");
      me.detachListeners("projectDestroy");
      delete me._crudManager;
      if (project) {
        projectListeners.thisObj = me;
        project.ion(projectListeners);
        if (project.isCrudManager) {
          me.crudManager = project;
        } else if (crudManager) {
          crudManager.project = project;
          me.crudManager = crudManager;
        }
        me.projectSubscribers.forEach((subscriber) => {
          subscriber.detachFromProject(oldProject);
          subscriber.attachToProject(project);
        });
        for (const storeName of me.constructor.projectStoreNames) {
          me[storeName] = project[storeName];
        }
        if (me.delayAutoLoad) {
          project.eventStore.autoLoad = true;
          project.eventStore.load();
        }
        (_a6 = project.stm) == null ? void 0 : _a6.ion({
          name: "projectConsumer",
          restoringStart: "onProjectRestoringStart",
          restoringStop: "onProjectRestoringStop",
          thisObj: me
        });
        project.ion({
          name: "projectDestroy",
          beforeDestroy: "onProjectBeforeDestroy",
          thisObj: me
        });
      }
      me.trigger("projectChange", { project });
    }
    // Implementation here because we need to get first look at it to adopt its stores
    changeCrudManager(crudManager) {
      if (this.buildingProjectConfig) {
        this._crudManager = crudManager.isCrudManager ? crudManager : Object.assign({}, crudManager);
      } else {
        return super.changeCrudManager(crudManager);
      }
    }
    updateCrudManager(crudManager) {
      if (!this.isConfiguring && crudManager) {
        this.updateStoresFromCrudManager();
      }
    }
    onProjectBeforeDestroy(event) {
      const emptyProject = new event.source.constructor();
      this.project = emptyProject;
    }
    // Called when project changes are committed, after data is written back to records
    onProjectDataReady() {
      const me = this;
      me.whenVisible(() => {
        if (me.projectCallbacks.size) {
          me.projectCallbacks.forEach((callback) => callback());
          me.projectCallbacks.clear();
        }
      }, null, null, "onProjectDataReady");
    }
    onProjectRestoringStart({ stm }) {
      const { rawQueue } = stm;
      if (rawQueue.length && rawQueue[rawQueue.length - 1].length > 1) {
        __privateSet(this, _suspendedByRestore, true);
        this.suspendRefresh();
      }
    }
    onProjectRestoringStop() {
      if (__privateGet(this, _suspendedByRestore)) {
        __privateSet(this, _suspendedByRestore, false);
        this.resumeRefresh(true);
      }
    }
    // Overridden in CalendarStores.js
    onBeforeTimeZoneChange() {
    }
    // When project changes time zone, change start and end dates
    onTimeZoneChange({ timeZone, oldTimeZone }) {
      const me = this;
      if (me.startDate && me.timeAxis.timeZone !== timeZone) {
        const startDate = oldTimeZone != null ? TimeZoneHelper.fromTimeZone(me.startDate, oldTimeZone) : me.startDate;
        me.startDate = timeZone != null ? TimeZoneHelper.toTimeZone(startDate, timeZone) : startDate;
        me.timeAxis.timeZone = timeZone;
      }
    }
    onStartApplyChangeset() {
      this.suspendRefresh();
    }
    onEndApplyChangeset() {
      this.resumeRefresh(true);
    }
    /**
     * Accepts a callback that will be called when the underlying project is ready (no commit pending and current commit
     * finalized)
     * @param {Function} callback
     * @category Data
     */
    whenProjectReady(callback) {
      if (this.isEngineReady) {
        callback();
      } else {
        this.projectCallbacks.add(callback);
      }
    }
    /**
     * Returns `true` if engine is in a stable calculated state, `false` otherwise.
     * @property {Boolean}
     * @category Misc
     */
    get isEngineReady() {
      var _a6, _b2;
      return Boolean((_b2 = (_a6 = this.project).isEngineReady) == null ? void 0 : _b2.call(_a6));
    }
    //endregion
    //region Destroy
    // Cleanup, destroys stores if this.destroyStores is true.
    doDestroy() {
      super.doDestroy();
      if (this.destroyStores) {
        !this.project.isDestroyed && this.project.destroy();
      }
    }
    //endregion
    get projectStores() {
      const { projectStoreNames } = this.constructor;
      return projectStoreNames.map((storeName) => this[storeName]);
    }
    static get projectStoreNames() {
      return Object.keys(this.projectStores);
    }
    static get projectDataNames() {
      return this.projectStoreNames.reduce((result, storeName) => {
        const { dataName } = this.projectStores[storeName];
        if (dataName) {
          result.push(dataName);
        }
        return result;
      }, []);
    }
    static setupProjectStores(cls, meta) {
      const { projectStores } = cls;
      if (projectStores) {
        const projectListeners = {
          name: "projectConsumer",
          dataReady: "onProjectDataReady",
          change: "relayProjectDataChange",
          beforeTimeZoneChange: "onBeforeTimeZoneChange",
          timeZoneChange: "onTimeZoneChange",
          // Next 4 handles only suspend/resume view refresh
          startApplyChangeset: "onStartApplyChangeset",
          endApplyChangeset: "onEndApplyChangeset",
          startApplyingRevision: "onStartApplyChangeset",
          stopApplyingRevision: "onEndApplyChangeset"
        }, storeConfigs = {
          projectListeners
        };
        let previousDataName;
        for (const storeName in projectStores) {
          const { dataName } = projectStores[storeName];
          storeConfigs[storeName] = storeConfigs[dataName] = null;
          if (dataName) {
            Object.defineProperty(meta.class.prototype, dataName, {
              configurable: true,
              // So that Config can add its setter.
              get() {
                var _a6;
                return (_a6 = this.project[storeName]) == null ? void 0 : _a6.records;
              }
            });
            this.createDataUpdater(storeName, dataName, previousDataName, meta);
          }
          this.createStoreDescriptor(meta, storeName, projectStores[storeName], projectListeners);
          previousDataName = dataName;
        }
        this.setupConfigs(meta, storeConfigs);
      }
    }
    static createDataUpdater(storeName, dataName, previousDataName, meta) {
      meta.class.prototype[`update${StringHelper.capitalize(dataName)}`] = function(data) {
        const { project } = this;
        previousDataName && this.getConfig(previousDataName);
        if (this.buildingProjectConfig) {
          project[`${dataName}Data`] = data;
        } else {
          project[storeName].data = data;
        }
      };
    }
    // eslint-disable-next-line bryntum/no-listeners-in-lib
    static createStoreDescriptor(meta, storeName, { listeners }, projectListeners) {
      const { prototype: clsProto } = meta.class, storeNameCap = StringHelper.capitalize(storeName);
      projectListeners[`${storeName}Change`] = function({ store }) {
        this[storeName] = store;
      };
      clsProto[`change${storeNameCap}`] = function(store, oldStore) {
        const me = this, { project } = me, storeProject = store == null ? void 0 : store.project;
        if (me.buildingProjectConfig) {
          if (storeProject == null ? void 0 : storeProject.isProjectModel) {
            me._sharedProject = storeProject;
          }
          project[storeName] = store;
          return;
        }
        if (!me.initializingProject) {
          if (project[storeName] !== store) {
            project[`set${storeNameCap}`](store);
            store = project[storeName];
          }
        }
        if (store !== oldStore) {
          if (listeners) {
            listeners.thisObj = me;
            listeners.name = `${storeName}Listeners`;
            listeners.startApplyChangeset = "onProjectStoreStartApplyChangeset";
            listeners.endApplyChangeset = "onProjectStoreEndApplyChangeset";
            me.detachListeners(listeners.name);
            store.ion(listeners);
          }
          me[`_${storeName}`] = store;
          me.projectSubscribers.forEach((subscriber) => {
            var _a6;
            (_a6 = subscriber[`attachTo${storeNameCap}`]) == null ? void 0 : _a6.call(subscriber, store);
          });
          me[`_${storeName}`] = null;
        }
        return store;
      };
    }
    onProjectStoreStartApplyChangeset() {
      this.suspendRefresh();
    }
    onProjectStoreEndApplyChangeset() {
      this.resumeRefresh(true);
    }
    relayProjectDataChange(event) {
      if ((event.isExpand || event.isCollapse) && !event.records[0].fieldMap.expanded.persist) {
        return;
      }
      return this.trigger("dataChange", { project: event.source, ...event, source: this });
    }
    //region WidgetClass
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
    //endregion
  }, _suspendedByRestore = new WeakMap(), __publicField(_a5, "$name", "ProjectConsumer"), _a5;
};

// ../Scheduler/lib/Scheduler/view/mixin/SchedulerStores.js
var SchedulerStores_default = (Target) => {
  var _a5;
  return _a5 = class extends ProjectConsumer_default(Target || Base) {
    //region Default config
    // This is the static definition of the Stores we consume from the project, and
    // which we must provide *TO* the project if we or our CrudManager is configured
    // with them.
    // The property name is the store name, and within that there is the dataName which
    // is the property which provides static data definition. And there is a listeners
    // definition which specifies the listeners *on this object* for each store.
    //
    // To process incoming stores, implement an updateXxxxxStore method such
    // as `updateEventStore(eventStore)`.
    //
    // To process an incoming Project implement `updateProject`. __Note that
    // `super.updateProject(...arguments)` must be called first.__
    static get projectStores() {
      return {
        resourceStore: {
          dataName: "resources"
        },
        eventStore: {
          dataName: "events",
          // eslint-disable-next-line bryntum/no-listeners-in-lib
          listeners: {
            batchedUpdate: "onEventStoreBatchedUpdate",
            changePreCommit: "onInternalEventStoreChange",
            commitStart: "onEventCommitStart",
            commit: "onEventCommit",
            exception: "onEventException",
            idchange: "onEventIdChange",
            beforeLoad: "onBeforeLoad"
          }
        },
        assignmentStore: {
          dataName: "assignments",
          // eslint-disable-next-line bryntum/no-listeners-in-lib
          listeners: {
            changePreCommit: "onAssignmentChange",
            // In EventSelection.js
            commitStart: "onAssignmentCommitStart",
            commit: "onAssignmentCommit",
            exception: "onAssignmentException",
            beforeRemove: {
              fn: "onAssignmentBeforeRemove",
              // We must go last in case an app vetoes a remove
              // by returning false from a handler.
              prio: -1e3
            }
          }
        },
        dependencyStore: {
          dataName: "dependencies"
        },
        calendarManagerStore: {},
        timeRangeStore: {},
        resourceTimeRangeStore: {}
      };
    }
    static get configurable() {
      return {
        /**
         * Overridden to *not* auto create a store at the Scheduler level.
         * The store is the {@link Scheduler.data.ResourceStore} of the backing project
         * @config {Core.data.Store}
         * @private
         */
        store: null,
        /**
         * The name of the start date parameter that will be passed to in every `eventStore` load request.
         * @config {String}
         * @category Data
         */
        startParamName: "startDate",
        /**
         * The name of the end date parameter that will be passed to in every `eventStore` load request.
         * @config {String}
         * @category Data
         */
        endParamName: "endDate",
        /**
         * Set to `true` to include `startDate` and `endDate` params indicating the currently viewed date range in
         * `EventStore` load requests (when loading using `AjaxStore` or `CrudManager` functionality).
         *
         * Dates are formatted using the same format as the `startDate` field on the `EventModel`
         * (e.g. 2023-03-08T00:00:00+01:00).
         *
         * @config {Boolean}
         * @default
         */
        passStartEndParameters: true,
        /**
         * Class that should be used to instantiate a CrudManager in case it's provided as a simple object to
         * {@link #config-crudManager} config.
         * @config {Scheduler.data.CrudManager}
         * @typings {typeof CrudManager}
         * @category Data
         */
        crudManagerClass: null,
        /**
         * Get/set the CrudManager instance
         * @member {Scheduler.data.CrudManager} crudManager
         * @category Data
         */
        /**
         * Supply a {@link Scheduler.data.CrudManager} instance or a config object if you want to use
         * CrudManager for handling data.
         * @config {CrudManagerConfig|Scheduler.data.CrudManager}
         * @category Data
         */
        crudManager: null
      };
    }
    //endregion
    //region Project
    updateProject(project, oldProject) {
      super.updateProject(project, oldProject);
      this.detachListeners("schedulerStores");
      project.ion({
        name: "schedulerStores",
        refresh: "onProjectRefresh",
        thisObj: this
      });
      this.bindEventStoreForEarlyPositioning(project.eventStore);
    }
    // Called when project changes are committed, before data is written back to records (but still ready to render
    // since data is fetched from engine)
    onProjectRefresh({ isInitialCommit }) {
      const me = this;
      if (me.isVisible) {
        if (isInitialCommit) {
          if (me.isVertical) {
            me.refreshAfterProjectRefresh = false;
            me.refreshWithTransition();
          }
        }
        if (me.refreshAfterProjectRefresh) {
          me.refreshWithTransition(false, !isInitialCommit);
          me.refreshAfterProjectRefresh = false;
        }
        if (me.navigateToAfterRefresh) {
          me.navigateTo(me.navigateToAfterRefresh, {
            scrollIntoView: false
          });
          me.navigateToAfterRefresh = null;
        }
      } else {
        me.whenVisible("refresh", me, [true]);
      }
    }
    //endregion
    //region CrudManager
    changeCrudManager(crudManager) {
      const me = this;
      if (crudManager && !crudManager.isCrudManager) {
        crudManager = me.crudManagerClass.new({
          scheduler: me
        }, crudManager);
      }
      me._crudManager = crudManager;
      me.bindCrudManager(crudManager);
    }
    //endregion
    //region Row store
    get store() {
      if (!this._store && this.isVertical) {
        this._store = new Store({
          data: [{
            id: "verticalTimeAxisRow",
            cls: "b-verticaltimeaxis-row"
          }],
          // This dummy store must be immune to the default operation
          // of any filtering features.
          filter: () => false
        });
      }
      return super.store;
    }
    set store(store) {
      super.store = store;
    }
    // Wrap w/ transition refreshFromRowOnStoreAdd() inherited from Grid
    refreshFromRowOnStoreAdd(row, { isExpand, records }) {
      const args = arguments, me = this;
      me.runWithTransition(() => {
        me.currentOrientation.suspended = !isExpand && !records.some((r) => r.isLinked);
        me.element.classList.add("b-no-transitions");
        super.refreshFromRowOnStoreAdd(row, ...args);
        me.element.classList.remove("b-no-transitions");
        me.currentOrientation.suspended = false;
      }, !isExpand);
    }
    onStoreAdd(event) {
      super.onStoreAdd(event);
      if (this.isPainted) {
        this.calculateRowHeights(event.records);
      }
    }
    onStoreUpdateRecord({ source: store, record, changes }) {
      let ignoreCount = 0;
      if ("assigned" in changes) {
        ignoreCount++;
      }
      if ("calendar" in changes) {
        ignoreCount++;
      }
      if (ignoreCount !== Object.keys(changes).length) {
        super.onStoreUpdateRecord(...arguments);
      }
    }
    //endregion
    //region ResourceStore
    updateResourceStore(resourceStore) {
      if (resourceStore && this.isHorizontal) {
        resourceStore.metaMapId = this.id;
        this.store = resourceStore;
      }
    }
    get usesDisplayStore() {
      return this.store !== this.resourceStore;
    }
    //endregion
    //region Events
    onEventIdChange(params) {
      var _a6, _b2;
      (_b2 = (_a6 = this.currentOrientation).onEventStoreIdChange) == null ? void 0 : _b2.call(_a6, params);
    }
    /**
     * Listener to the batchedUpdate event which fires when a field is changed on a record which
     * is batch updating. Occasionally UIs must keep in sync with batched changes.
     * For example, the EventResize feature performs batched updating of the startDate/endDate
     * and it tells its client to listen to batchedUpdate.
     * @private
     */
    onEventStoreBatchedUpdate(event) {
      if (this.listenToBatchedUpdates) {
        return this.onInternalEventStoreChange(event);
      }
    }
    /**
     * Calls appropriate functions for current event layout when the event store is modified.
     * @private
     */
    // Named as Internal to avoid naming collision with wrappers that relay events
    onInternalEventStoreChange(params) {
      if (!this.isPainted || !this._mode || params.isAssign || this.assignmentStore.isRemovingAssignment) {
        return;
      }
      if (this.isVisible) {
        this.currentOrientation.onEventStoreChange(params);
      } else {
        this.whenVisible(this.onInternalEventStoreChange, this, [params]);
      }
    }
    /**
     * Refreshes committed events, to remove dirty/committing flag.
     * CSS is added
     * @private
     */
    onEventCommit({ changes }) {
      let resourcesToRepaint = [...changes.added, ...changes.modified].map(
        (eventRecord) => this.eventStore.getResourcesForEvent(eventRecord)
      );
      resourcesToRepaint = Array.prototype.concat.apply([], resourcesToRepaint);
      new Set(resourcesToRepaint).forEach(
        (resourceRecord) => this.repaintEventsForResource(resourceRecord)
      );
    }
    /**
     * Adds the committing flag to changed events before commit.
     * @private
     */
    onEventCommitStart({ changes }) {
      const { currentOrientation, committingCls } = this;
      [...changes.added, ...changes.modified].forEach(
        (eventRecord) => eventRecord.assignments.forEach(
          (assignmentRecord) => currentOrientation.toggleCls(assignmentRecord, committingCls, true)
        )
      );
    }
    // Clear committing flag
    onEventException({ action }) {
      if (action === "commit") {
        const { changes } = this.eventStore;
        [...changes.added, ...changes.modified, ...changes.removed].forEach(
          (eventRecord) => this.repaintEvent(eventRecord)
        );
      }
    }
    onAssignmentCommit({ changes }) {
      this.repaintEventsForAssignmentChanges(changes);
    }
    onAssignmentCommitStart({ changes }) {
      const { currentOrientation, committingCls } = this;
      [...changes.added, ...changes.modified].forEach((assignmentRecord) => {
        currentOrientation.toggleCls(assignmentRecord, committingCls, true);
      });
    }
    // Clear committing flag
    onAssignmentException({ action }) {
      if (action === "commit") {
        this.repaintEventsForAssignmentChanges(this.assignmentStore.changes);
      }
    }
    repaintEventsForAssignmentChanges(changes) {
      const resourcesToRepaint = [...changes.added, ...changes.modified, ...changes.removed].map(
        (assignmentRecord) => assignmentRecord.getResource()
      );
      new Set(resourcesToRepaint).forEach(
        (resourceRecord) => this.repaintEventsForResource(resourceRecord)
      );
    }
    onAssignmentBeforeRemove({ records, removingAll }) {
      if (removingAll) {
        return;
      }
      const me = this, { activeAssignment } = me, deletingActiveAssignment = records.includes(activeAssignment);
      let moveTo;
      if (!me.isConfiguring && // If we have current active assignment or we scheduled navigating to an assignment, we should check
      // if we're removing that assignment in order to avoid navigating to it
      (me.navigateToAfterRefresh || activeAssignment && deletingActiveAssignment)) {
        if (records.includes(me.navigateToAfterRefresh)) {
          me.navigateToAfterRefresh = null;
        }
        const fromEl = me.getElementFromAssignmentRecord(activeAssignment, true);
        if (deletingActiveAssignment && fromEl) {
          const viewport = me.timeAxisSubGrid.rectangle().intersect(me._bodyRectangle), fromRect = Rectangle.from(fromEl), fromCenter = fromRect.center, resourceDim = me.isHorizontal ? "y" : "x", distanceSort = ({ element: e13, distance: d1, center: c1, edgeDistance: ed1 }, { element: e22, distance: d2, center: c2, edgeDistance: ed2 }) => {
            if (e13.dataset.resourceId !== e22.dataset.resourceId) {
              if (Math.abs(c1[resourceDim] - fromCenter[resourceDim]) < Math.abs(c2[resourceDim] - fromCenter[resourceDim])) {
                return -1;
              }
              if (Math.abs(c1[resourceDim] - fromCenter[resourceDim]) > Math.abs(c2[resourceDim] - fromCenter[resourceDim])) {
                return 1;
              }
            }
            return Math.min(ed1, d1) - Math.min(ed2, d2) || c2[resourceDim] - c1[resourceDim];
          }, to = Array.from(me.foregroundCanvas.querySelectorAll(me.navigator.itemSelector)).reduce((result, element) => {
            if (element !== fromEl) {
              const rectangle = Rectangle.from(element).intersect(viewport), assignment = rectangle && me.resolveAssignmentRecord(element);
              if (assignment) {
                const { center } = rectangle, d = fromCenter.getDelta(center), vertGap = center.y < fromCenter.y ? fromRect.y - rectangle.bottom : center.y > fromCenter.y ? rectangle.y - fromRect.bottom : 0, horizGap = center.x < fromCenter.x ? fromRect.x - rectangle.right : center.x > fromCenter.x ? rectangle.x - fromRect.right : 0;
                result.push({
                  element,
                  assignment,
                  center,
                  distance: Math.sqrt(d[0] ** 2 + d[1] ** 2),
                  edgeDistance: Math.max(
                    vertGap,
                    horizGap
                  )
                });
              }
            }
            return result;
          }, []).sort(distanceSort)[0];
          if (to) {
            moveTo = to.assignment;
          }
        }
        if (moveTo) {
          me.navigateTo(moveTo, {
            scrollIntoView: false
          });
          me.navigateToAfterRefresh = moveTo;
        } else {
          me.focusElement.focus({ preventScroll: true });
        }
      }
    }
    //endregion
    //region TimeRangeStore & TimeRanges
    /**
     * Inline time ranges, will be loaded into an internally created store if {@link Scheduler.feature.TimeRanges}
     * is enabled.
     * @config {Scheduler.model.TimeSpan[]|TimeSpanConfig[]} timeRanges
     * @non-lazy-load
     * @category Data
     */
    /**
     * Get/set time ranges, applies to the backing project's TimeRangeStore.
     * @member {Scheduler.model.TimeSpan[]} timeRanges
     * @accepts {Scheduler.model.TimeSpan[]|TimeSpanConfig[]}
     * @category Data
     */
    /**
     * Get/set the time ranges store instance or config object for {@link Scheduler.feature.TimeRanges} feature.
     * @member {Core.data.Store} timeRangeStore
     * @accepts {Core.data.Store|StoreConfig}
     * @category Data
     */
    /**
     * The time ranges store instance for {@link Scheduler.feature.TimeRanges} feature.
     * @config {Core.data.Store|StoreConfig} timeRangeStore
     * @category Data
     */
    set timeRanges(timeRanges) {
      this.project.timeRanges = timeRanges;
    }
    get timeRanges() {
      return this.project.timeRanges;
    }
    //endregion
    //region ResourceTimeRangeStore
    /**
     * Inline resource time ranges, will be loaded into an internally created store if
     * {@link Scheduler.feature.ResourceTimeRanges} is enabled.
     * @prp {Scheduler.model.ResourceTimeRangeModel[]} resourceTimeRanges
     * @non-lazy-load
     * @accepts {Scheduler.model.ResourceTimeRangeModel[]|ResourceTimeRangeModelConfig[]}
     * @category Data
     */
    /**
     * Get/set the resource time ranges store instance for {@link Scheduler.feature.ResourceTimeRanges} feature.
     * @member {Scheduler.data.ResourceTimeRangeStore} resourceTimeRangeStore
     * @accepts {Scheduler.data.ResourceTimeRangeStore|ResourceTimeRangeStoreConfig}
     * @category Data
     */
    /**
     * Resource time ranges store instance or config object for {@link Scheduler.feature.ResourceTimeRanges} feature.
     * @config {Scheduler.data.ResourceTimeRangeStore|ResourceTimeRangeStoreConfig} resourceTimeRangeStore
     * @category Data
     */
    set resourceTimeRanges(resourceTimeRanges) {
      this.project.resourceTimeRanges = resourceTimeRanges;
    }
    get resourceTimeRanges() {
      return this.project.resourceTimeRanges;
    }
    //endregion
    //region Other functions
    onBeforeLoad({ params }) {
      this.applyStartEndParameters(params);
    }
    /**
     * Get events grouped by timeAxis ticks from resources array
     * @category Data
     * @param {Scheduler.model.ResourceModel[]} resources An array of resources to process. If not passed, all resources
     * will be used.
     * @param {Function} filterFn filter function to filter events if required. Optional.
     * @private
     */
    getResourcesEventsPerTick(resources, filterFn) {
      const { timeAxis, resourceStore } = this, eventsByTick = [];
      resources = resources || resourceStore.records;
      resources.forEach((resource) => {
        resource.events.forEach((event) => {
          if (!timeAxis.isTimeSpanInAxis(event) || filterFn && !filterFn.call(this, { resource, event })) {
            return;
          }
          let startTick = Math.floor(timeAxis.getTickFromDate(event.startDate)), endTick = Math.ceil(timeAxis.getTickFromDate(event.endDate));
          if (startTick == -1) {
            startTick = 0;
          }
          if (endTick === -1) {
            endTick = timeAxis.ticks.length;
          }
          do {
            if (!eventsByTick[startTick]) {
              eventsByTick[startTick] = [event];
            } else {
              eventsByTick[startTick].push(event);
            }
          } while (++startTick < endTick);
        });
      });
      return eventsByTick;
    }
    //endregion
    //region WidgetClass
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
    //endregion
  }, __publicField(_a5, "$name", "SchedulerStores"), _a5;
};

// ../Scheduler/lib/Scheduler/view/mixin/SchedulerScroll.js
var defaultScrollOptions5 = {
  block: "nearest",
  edgeOffset: 20
};
var SchedulerScroll_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    //region Scroll to event
    /**
     * Scrolls an event record into the viewport.
     * If the resource store is a tree store, this method will also expand all relevant parent nodes to locate the event.
     *
     * This function is not applicable for events with multiple assignments, please use #scrollResourceEventIntoView instead.
     *
     * @param {Scheduler.model.EventModel} eventRecord the event record to scroll into view
     * @param {BryntumScrollOptions} [options] How to scroll.
     * @returns {Promise} A Promise which resolves when the scrolling is complete.
     * @async
     * @category Scrolling
     */
    async scrollEventIntoView(eventRecord, options = defaultScrollOptions5) {
      const me = this, resources = eventRecord.resources || [eventRecord];
      if (resources.length > 1) {
        throw new Error("scrollEventIntoView() is not applicable for events with multiple assignments, please use scrollResourceEventIntoView() instead.");
      }
      if (!resources.length) {
        console.warn("You have asked to scroll to an event which is not assigned to a resource");
        return;
      }
      await me.scrollResourceEventIntoView(resources[0], eventRecord, options);
    }
    /**
     * Scrolls an assignment record into the viewport.
     *
     * If the resource store is a tree store, this method will also expand all relevant parent nodes
     * to locate the event.
     *
     * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record
     * @param {BryntumScrollOptions} [options] How to scroll.
     * @returns {Promise} A Promise which resolves when the scrolling is complete.
     * @category Scrolling
     */
    scrollAssignmentIntoView(assignmentRecord, ...args) {
      return this.scrollResourceEventIntoView(assignmentRecord.resource, assignmentRecord.event, ...args);
    }
    /**
     * Scrolls a resource event record into the viewport.
     *
     * If the resource store is a tree store, this method will also expand all relevant parent nodes
     * to locate the event.
     *
     * @param {Scheduler.model.ResourceModel} resourceRecord The resource record
     * @param {Scheduler.model.EventModel} eventRecord An event record to scroll into view
     * @param {BryntumScrollOptions} [options] How to scroll.
     * @returns {Promise} A Promise which resolves when the scrolling is complete.
     * @category Scrolling
     * @async
     */
    async scrollResourceEventIntoView(resourceRecord, eventRecord, options = defaultScrollOptions5) {
      var _a6, _b2, _c2;
      if (options.edgeOffset == null) {
        options.edgeOffset = 20;
      }
      const me = this, {
        store,
        timeAxis,
        visibleDateRange,
        timelineScroller,
        isVertical
      } = me, {
        unfilteredStartDate,
        unfilteredEndDate
      } = timeAxis, currentTimeSpanRange = unfilteredEndDate - unfilteredStartDate, eventStart = eventRecord.startDate, eventEnd = eventRecord.endDate, eventStartMs = eventRecord.isScheduled && eventStart.getTime(), eventEndMs = eventRecord.isScheduled && eventEnd.getTime(), eventMidpoint = (eventStartMs + eventEndMs) / 2, timespanRange = visibleDateRange.endMS - visibleDateRange.startMS, halfTimeSpanRange = timespanRange / 2, nearestEdge = Math.abs(eventStartMs - visibleDateRange.startMS) < Math.abs(eventEndMs - visibleDateRange.endMS) ? "start" : "end", targetEdge = !options.block || options.block === "nearest" ? nearestEdge : options.block, tickDuration = new Duration(timeAxis.increment, timeAxis.unit), eventDateRequested = targetEdge === "start" ? eventStart : targetEdge === "end" ? eventEnd : new Date(eventMidpoint), edgeOffsetDuration = new Duration(options.edgeOffset * me.getMilliSecondsPerPixelForZoomLevel(me.viewPreset), "ms");
      resourceRecord = (_a6 = resourceRecord.getStoreLink(isVertical ? me.resourceStore : store)) != null ? _a6 : resourceRecord;
      await me.waitForReadiness();
      if (me.isLockedRows) {
        me.syncSplits((other) => other.scrollResourceEventIntoView(resourceRecord, eventRecord, options));
      }
      if (!eventRecord.isScheduled) {
        return me.scrollResourceIntoView(resourceRecord, options);
      }
      options = {
        ...options,
        // Time timension
        [isVertical ? "block" : "inline"]: options.block,
        // Resource dimension
        [isVertical ? "inline" : "block"]: "nearest"
      };
      if (!DateHelper.betweenLesserEqual(eventDateRequested, unfilteredStartDate, unfilteredEndDate) && options.extendTimeAxis !== false) {
        const targetDate = targetEdge === "start" ? DateHelper.floor(eventStart, tickDuration) : targetEdge === "end" ? DateHelper.ceil(eventEnd, tickDuration) : eventDateRequested;
        if (me.infiniteScroll) {
          const extendedTimeSpanRange = me.calculateInfiniteScrollingDateRange(DateHelper.add(targetDate, halfTimeSpanRange * (targetEdge === "start" ? 1 : -1), "ms"), true), visibleDate = targetEdge === "center" ? new Date((eventStartMs + eventEndMs) / 2) : targetEdge === "end" ? DateHelper.ceil(new Date(eventEndMs + halfTimeSpanRange), tickDuration) : DateHelper.floor(new Date(eventStartMs - halfTimeSpanRange), tickDuration);
          me.setTimeSpan(
            extendedTimeSpanRange.startDate,
            extendedTimeSpanRange.endDate,
            {
              visibleDate,
              block: "center"
            }
          );
        } else {
          const s = DateHelper.add(eventDateRequested, edgeOffsetDuration.negate()), e = DateHelper.add(eventDateRequested, edgeOffsetDuration), range = targetEdge === "start" ? [s, DateHelper.add(s, currentTimeSpanRange, "ms")] : targetEdge === "end" ? [DateHelper.add(e, -currentTimeSpanRange, "ms"), e] : [DateHelper.add(eventDateRequested, -currentTimeSpanRange / 2, "ms"), DateHelper.add(eventDateRequested, currentTimeSpanRange / 2, "ms")];
          await me.setTimeSpan(
            range[0],
            range[1],
            {
              visibleDate: targetDate,
              block: targetEdge
            }
          );
        }
        options = {
          ...options,
          [isVertical ? "block" : "inline"]: targetEdge
        };
      }
      if (me.isDestroyed) {
        return;
      }
      if (store.tree) {
        await ((_b2 = me.expandTo) == null ? void 0 : _b2.call(me, resourceRecord));
      }
      if (store.isGrouped) {
        await store.expand(store.getGroupHeaderForRecord(resourceRecord));
      }
      if (((_c2 = me.features.nestedEvents) == null ? void 0 : _c2.enabled) && eventRecord.parent && !eventRecord.parent.isRoot) {
        await me.scrollEventIntoView(eventRecord.parent);
      }
      let el = me.getElementFromEventRecord(eventRecord, resourceRecord);
      if (el) {
        if (!DomHelper.isFocusable(el)) {
          el = el.parentNode;
        }
        await timelineScroller.scrollIntoView(el, options);
      } else if (!eventRecord.isOccurrence && me.eventStore.isFilteredOut(eventRecord)) {
        console.warn("You have asked to scroll to an event which is not available");
      } else {
        await me.scrollUnrenderedEventIntoView(resourceRecord, eventRecord, {
          ...options,
          [isVertical ? "inline" : "block"]: "nearest"
        });
      }
    }
    /**
     * Scrolls an unrendered event into view. Internal function used from #scrollResourceEventIntoView.
     * @private
     * @category Scrolling
     */
    scrollUnrenderedEventIntoView(resourceRec, eventRec, options = defaultScrollOptions5) {
      return new Promise(async (resolve) => {
        const me = this, scroller = me.timeAxisSubGrid.scrollable, { viewport } = scroller;
        if (!viewport) {
          resolve();
          return;
        }
        let eventElement, delta, counter = 0;
        do {
          if (++counter >= 50) {
            throw new Error(`Too many preparational scrolls during 'scrollIntoView' for event id = ${eventRec.id}`);
          }
          const box = me.getResourceEventBox(eventRec, resourceRec);
          if (!box) {
            resolve();
            return;
          }
          box.x = Math.ceil(box.x);
          box.y = Math.ceil(box.y);
          if (me.rtl) {
            box._x = -(box.x - viewport.width);
          }
          box.translate(viewport.x - scroller.scrollLeft, viewport.y - scroller.y);
          if (delta === void 0) {
            delta = scroller.getDeltaTo(box, options);
          }
          const scrollPromise3 = scroller.scrollIntoView(box, options);
          await scrollPromise3;
          if (scrollPromise3.cancelled || me.isDestroyed) {
            resolve();
            return true;
          }
          await AsyncHelper.animationFrame();
          if (me.isDestroyed) {
            resolve();
            return true;
          }
          eventElement = me.getElementFromEventRecord(eventRec, resourceRec);
        } while (!eventElement);
        me.fixElementHeights();
        const scrollPromise2 = scroller.scrollIntoView(
          eventElement,
          { ...options, force: true }
        );
        await scrollPromise2;
        if (scrollPromise2.canceled || me.isDestroyed) {
          resolve();
          return true;
        }
        resolve();
      });
    }
    /**
     * Scrolls the specified resource into view, works for both horizontal and vertical modes.
     * @param {Scheduler.model.ResourceModel} resourceRecord A resource record
     * @param {BryntumScrollOptions} [options] How to scroll.
     * @returns {Promise} A promise which is resolved when the scrolling has finished.
     * @category Scrolling
     */
    async scrollResourceIntoView(resourceRecord, options = defaultScrollOptions5) {
      await this.waitForReadiness();
      if (this.isVertical) {
        await this.currentOrientation.scrollResourceIntoView(resourceRecord, options);
      } else {
        await this.scrollRowIntoView(resourceRecord, options);
      }
    }
    //endregion
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "SchedulerScroll"), __publicField(_a5, "configurable", {
    /**
     * Specifies the maximum duration for extending the current {@link Scheduler.model.TimeSpan} to allow for smooth
     * scrolling to an event.
     *
     * This configuration determines how far the scheduler should extend the visible timespan to enable smooth
     * scrolling. If the target event is within this duration from the current view, the scheduler will animate the
     * scroll. If the event lies beyond this threshold, the scheduler will directly jump to it without animation.
     *
     * Setting a shorter duration can enhance performance in views with many events, as it limits the number of
     * events that need to be rendered when extending the timespan.
     *
     * The value can be specified as a string or a {@link DurationConfig} object, allowing for flexible duration
     * definitions, such as '1 week' for one week or `{unit: 'week', magnitude: 1}`.
     *
     * Default value is the duration of the currently rendered timespan at the time of invocation.
     * @config {String|DurationConfig}
     */
    scrollExtensionThreshold: null
  }), _a5;
};

// ../Scheduler/lib/Scheduler/view/mixin/SchedulerRegions.js
var SchedulerRegions_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    //region Orientation dependent regions
    /**
     * Gets the region represented by the schedule and optionally only for a single resource. The view will ask the
     * scheduler for the resource availability by calling getResourceAvailability. By overriding that method you can
     * constrain events differently for different resources.
     * @param {Scheduler.model.ResourceModel} resourceRecord (optional) The resource record
     * @param {Scheduler.model.EventModel} eventRecord (optional) The event record
     * @returns {Core.helper.util.Rectangle} The region of the schedule
     */
    getScheduleRegion(resourceRecord, eventRecord, local = true, dateConstraints) {
      return this.currentOrientation.getScheduleRegion(...arguments);
    }
    /**
     * Gets the region, relative to the timeline view element, representing the passed resource and optionally just for a certain date interval.
     * @param {Scheduler.model.ResourceModel} resourceRecord The resource record
     * @param {Date} startDate A start date constraining the region
     * @param {Date} endDate An end date constraining the region
     * @returns {Core.helper.util.Rectangle} A Rectangle which encapsulates the resource time span
     */
    getResourceRegion(resourceRecord, startDate, endDate) {
      return this.currentOrientation.getRowRegion(...arguments);
    }
    //endregion
    //region ResourceEventBox
    getAssignmentEventBox(assignmentRecord, includesOutside) {
      return this.getResourceEventBox(assignmentRecord.event, assignmentRecord.resource, includesOutside);
    }
    /**
     * Get the region for a specified resources specified event.
     * @param {Scheduler.model.EventModel} eventRecord
     * @param {Scheduler.model.ResourceModel} resourceRecord
     * @param {Boolean} includeOutside Specify true to get boxes for events outside of the rendered zone in both
     *   dimensions. This option is used when calculating dependency lines, and we need to include routes from events
     *   which may be outside the rendered zone.
     * @returns {Core.helper.util.Rectangle}
     */
    getResourceEventBox(eventRecord, resourceRecord, includeOutside = false, roughly = false) {
      return this.currentOrientation.getResourceEventBox(...arguments);
    }
    //endregion
    //region Item box
    /**
     * Gets box for displayed item designated by the record. If several boxes are displayed for the given item
     * then the method returns all of them. Box coordinates are in view coordinate system.
     *
     * Boxes outside scheduling view timeaxis timespan and inside collapsed rows (if row defining store is a tree store)
     * will not be returned. Boxes outside scheduling view vertical visible area (i.e. boxes above currently visible
     * top row or below currently visible bottom row) will be calculated approximately.
     *
     * @param {Scheduler.model.EventModel} event
     * @returns {Core.helper.util.Rectangle[]} Array of resource box rectangles for event resources
     * @internal
     */
    getItemBox(event, includeOutside = false) {
      return event.resources.map((resource) => this.getResourceEventBox(event, resource, includeOutside));
    }
    //endregion
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "SchedulerRegions"), _a5;
};

// ../Scheduler/lib/Scheduler/view/mixin/SchedulerState.js
var copyProperties2 = [
  "startDate",
  "endDate",
  "eventLayout",
  "barMargin",
  "mode",
  "tickSize",
  "eventColor",
  "eventStyle",
  "fillTicks"
];
var SchedulerState_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    /**
     * Gets or sets scheduler's state. Check out {@link Scheduler.view.mixin.SchedulerState} mixin
     * and {@link Grid.view.mixin.GridState} for more details.
     * @member {Object} state
     * @category State
     * @typings  {SchedulerStateInfo}
     */
    /**
     * Get scheduler's current state for serialization. State includes rowHeight, headerHeight, readOnly, selectedCell,
     * selectedRecordId, column states and store state etc. It also collects state from all features.
     * @returns {Object} State object to be serialized
     * @private
     */
    getState() {
      return ObjectHelper.copyProperties(super.getState(), this, copyProperties2);
    }
    /**
     * Apply previously stored state.
     * @param {Object} state
     * @private
     */
    applyState(state) {
      var _a6;
      state = Object.assign({}, state);
      this.suspendRefresh();
      if ((state == null ? void 0 : state.eventLayout) === "layoutFn") {
        delete state.eventLayout;
      }
      if ((_a6 = state == null ? void 0 : state.zoomLevelOptions) == null ? void 0 : _a6.width) {
        delete state.tickSize;
      }
      super.applyState(state);
      this.resumeRefresh(true, false);
    }
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "SchedulerState"), // region events
  /**
   * Fired before state is saved by the StateProvider. Allows editing the state object or preventing the operation.
   * @event beforeStateSave
   * @preventable
   * @param {SchedulerStateInfo} state State object config
   */
  /**
   * Fired before state is applied to the source. Allows editing the state object or preventing the operation.
   * @event beforeStateApply
   * @preventable
   * @param {SchedulerStateInfo} state State object config
   */
  // endregion
  __publicField(_a5, "configurable", {
    statefulEvents: ["dateRangeChange"]
  }), _a5;
};

// ../Scheduler/lib/Scheduler/view/mixin/EventSelection.js
var EventSelection_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    //region Default config
    static get configurable() {
      return {
        /**
         * Configure as `true`, or set property to `true` to highlight dependent events as well when selecting an event.
         * @config {Boolean}
         * @default
         * @category Selection
         */
        highlightPredecessors: false,
        /**
         * Configure as `true`, or set property to `true` to highlight dependent events as well when selecting an event.
         * @config {Boolean}
         * @default
         * @category Selection
         */
        highlightSuccessors: false,
        /**
         * Configure as `true` to deselect a selected event upon click.
         * @config {Boolean}
         * @default
         * @category Selection
         */
        deselectOnClick: false,
        /**
         * Configure as `false` to preserve selection when clicking the empty schedule area.
         * @config {Boolean}
         * @default
         * @category Selection
         */
        deselectAllOnScheduleClick: true,
        /**
         * Set to `false` to not select the resource of the event when clicking an event bar.
         * @prp {Boolean}
         * @default
         * @category Selection
         */
        selectResourceOnEventNavigate: true,
        /**
         * Set to `false` to not select the row/resource when clicking the empty area in a time axis cell.
         * @prp {Boolean}
         * @default
         * @category Selection
         */
        selectResourceOnScheduleClick: true,
        /**
         * Collection to store selection.
         * @config {Core.util.Collection}
         * @private
         */
        selectedCollection: {},
        /**
         * Configure as `true` to allow `CTRL/CMD+click` to select multiple events in the scheduler.
         *
         * Or pass an object to control which modifier keys to use for multi selection.
         *
         * @config {Boolean|Object}
         * @property {Boolean} [ctrlKey] Set to `true` to use `CTRL` key for multi selection (`CMD` on Mac).
         * @property {Boolean} [altKey] Set to `true` to use `ALT` key for multi selection (`Option` on Mac)
         * @property {Boolean} [shiftKey] Set to `true` to use `SHIFT` key for multi selection
         * @category Selection
         */
        multiEventSelect: false
      };
    }
    static get defaultConfig() {
      return {
        /**
         * Configure as `true`, or set property to `true` to disable event selection.
         * @config {Boolean}
         * @default
         * @category Selection
         */
        eventSelectionDisabled: false,
        /**
         * CSS class to add to selected events.
         * @config {String}
         * @default
         * @category CSS
         * @private
         */
        eventSelectedCls: "b-sch-event-selected",
        /**
         * Configure as `true` to trigger `selectionChange` when removing a selected event/assignment.
         * @config {Boolean}
         * @default
         * @category Selection
         */
        triggerSelectionChangeOnRemove: false,
        /**
         * This flag controls whether Scheduler should preserve its selection of events when loading a new dataset
         * (if selected event ids are included in the newly loaded dataset).
         * @config {Boolean}
         * @default
         * @category Selection
         */
        maintainSelectionOnDatasetChange: true,
        /**
         * CSS class to add to other instances of a selected event, to highlight them.
         * @config {String}
         * @default
         * @category CSS
         * @private
         */
        eventAssignHighlightCls: "b-sch-event-assign-selected"
      };
    }
    //endregion
    //region Events
    /**
     * Fired any time there is a change to the events selected in the Scheduler.
     * @event eventSelectionChange
     * @param {'select'|'deselect'|'update'|'clear'} action One of the actions 'select', 'deselect', 'update',
     * 'clear'
     * @param {Scheduler.model.EventModel[]} selected An array of the Events added to the selection.
     * @param {Scheduler.model.EventModel[]} deselected An array of the Event removed from the selection.
     * @param {Scheduler.model.EventModel[]} selection The new selection.
     */
    /**
     * Fired any time there is going to be a change to the events selected in the Scheduler.
     * Returning `false` prevents the change
     * @event beforeEventSelectionChange
     * @preventable
     * @param {String} action One of the actions 'select', 'deselect', 'update', 'clear'
     * @param {Scheduler.model.EventModel[]} selected An array of events that will be added to the selection.
     * @param {Scheduler.model.EventModel[]} deselected An array of events that will be removed from the selection.
     * @param {Scheduler.model.EventModel[]} selection The currently selected events, before applying `selected` and `deselected`.
     */
    /**
     * Fired any time there is a change to the assignments selected in the Scheduler.
     * @event assignmentSelectionChange
     * @param {'select'|'deselect'|'update'|'clear'} action One of the actions 'select', 'deselect', 'update',
     * 'clear'
     * @param {Scheduler.model.AssignmentModel[]} selected An array of the Assignments added to the selection.
     * @param {Scheduler.model.AssignmentModel[]} deselected An array of the Assignments removed from the selection.
     * @param {Scheduler.model.AssignmentModel[]} selection The new selection.
     */
    /**
     * Fired any time there is going to be a change to the assignments selected in the Scheduler.
     * Returning `false` prevents the change
     * @event beforeAssignmentSelectionChange
     * @preventable
     * @param {String} action One of the actions 'select', 'deselect', 'update', 'clear'
     * @param {Scheduler.model.EventModel[]} selected An array of assignments that will be added to the selection.
     * @param {Scheduler.model.EventModel[]} deselected An array of assignments that will be removed from the selection.
     * @param {Scheduler.model.EventModel[]} selection The currently selected assignments, before applying `selected` and `deselected`.
     */
    //endregion
    //region Init
    afterConstruct() {
      var _a6;
      super.afterConstruct();
      (_a6 = this.navigator) == null ? void 0 : _a6.ion({
        navigate: "onEventNavigate",
        thisObj: this
      });
    }
    doDestroy() {
      var _a6;
      super.doDestroy();
      ((_a6 = this._selectedCollection) == null ? void 0 : _a6.owner) === this && this._selectedCollection.destroy();
    }
    changeMultiEventSelect(value2) {
      if (value2 === true) {
        value2 = {
          ctrlKey: true
        };
      }
      return value2;
    }
    //endregion
    //region Selected Collection
    changeSelectedCollection(selectedCollection) {
      if (!selectedCollection.isCollection) {
        selectedCollection = new Collection(selectedCollection);
      }
      return selectedCollection;
    }
    updateSelectedCollection(selectedCollection) {
      const me = this;
      if (!selectedCollection.owner) {
        selectedCollection.owner = me;
      }
      selectedCollection.ion({
        change: (...args) => me.project.deferUntilRepopulationIfNeeded(
          "onSelectedCollectionChange",
          (...args2) => !me.isDestroying && me.onSelectedCollectionChange(...args2),
          args
        ),
        // deferring this handler breaks the UI
        beforeSplice: "onBeforeSelectedCollectionSplice",
        thisObj: me
      });
    }
    get selectedCollection() {
      return this._selectedCollection;
    }
    getActionType(selection, selected, deselected, before) {
      if (before && selection.length > 0 && deselected.length === selection.length && selected.length === 0 || !before && selection.length === 0 && deselected.length > 0) {
        return "clear";
      }
      return selected.length > 0 && deselected.length > 0 ? "update" : selected.length > 0 ? "select" : "deselect";
    }
    //endregion
    //region Modify selection
    getEventsFromAssignments(assignments) {
      return ArrayHelper.unique(assignments.map((assignment) => assignment.event));
    }
    /**
     * The {@link Scheduler.model.EventModel events} which are selected.
     * @property {Scheduler.model.EventModel[]}
     * @category Selection
     */
    get selectedEvents() {
      return this.getEventsFromAssignments(this.selectedCollection.values);
    }
    set selectedEvents(events) {
      const assignments = [];
      events = ArrayHelper.asArray(events);
      events == null ? void 0 : events.forEach((event) => {
        if (this.isEventSelectable(event) !== false) {
          if (event.isOccurrence) {
            event.assignments.forEach((as) => {
              assignments.push(this.assignmentStore.getOccurrence(as, event));
            });
          } else {
            assignments.push(...event.assignments);
          }
        }
      });
      this.selectedCollection.splice(0, this.selectedCollection.count, assignments);
    }
    /**
     * The {@link Scheduler.model.AssignmentModel events} which are selected.
     * @property {Scheduler.model.AssignmentModel[]}
     * @category Selection
     */
    get selectedAssignments() {
      return this.selectedCollection.values;
    }
    set selectedAssignments(assignments) {
      this.selectedCollection.splice(0, this.selectedCollection.count, assignments || []);
    }
    /**
     * Returns `true` if the {@link Scheduler.model.EventModel event} is selected.
     * @param {Scheduler.model.EventModel} event The event
     * @returns {Boolean} Returns `true` if the event is selected
     * @category Selection
     */
    isEventSelected(event) {
      const { selectedCollection } = this;
      return Boolean(selectedCollection.count && selectedCollection.includes(event.assignments));
    }
    /**
     * A template method (empty by default) allowing you to control if an event can be selected or not.
     *
     * ```javascript
     * new Scheduler({
     *     isEventSelectable(event) {
     *         return event.startDate >= Date.now();
     *     }
     * })
     * ```
     *
     * This selection process is applicable to calendar too:
     *
     * ```javascript
     * new Calendar({
     *     isEventSelectable(event) {
     *         return event.startDate >= Date.now();
     *     }
     * })
     * ```
     *
     * @param {Scheduler.model.EventModel} event The event record
     * @returns {Boolean} true if event can be selected, otherwise false
     * @prp {Function}
     * @category Selection
     */
    isEventSelectable(event) {
    }
    /**
     * Returns `true` if the {@link Scheduler.model.AssignmentModel assignment} is selected.
     * @param {Scheduler.model.AssignmentModel} assignment The assignment
     * @returns {Boolean} Returns `true` if the assignment is selected
     * @category Selection
     */
    isAssignmentSelected(assignment) {
      return this.selectedCollection.includes(assignment);
    }
    /**
     * Selects the passed {@link Scheduler.model.EventModel event} or {@link Scheduler.model.AssignmentModel assignment}
     * *if it is not selected*. Selecting events results in all their assignments being selected.
     * @param {Scheduler.model.EventModel|Scheduler.model.AssignmentModel} eventOrAssignment The event or assignment to select
     * @param {Boolean} [preserveSelection] Pass `true` to preserve any other selected events or assignments
     * @category Selection
     */
    select(eventOrAssignment, preserveSelection = false) {
      if (eventOrAssignment.isAssignment) {
        this.selectAssignment(eventOrAssignment, preserveSelection);
      } else {
        this.selectEvent(eventOrAssignment, preserveSelection);
      }
    }
    /**
     * Selects the passed {@link Scheduler.model.EventModel event} *if it is not selected*. Selecting an event will
     * select all its assignments.
     * @param {Scheduler.model.EventModel} event The event to select
     * @param {Boolean} [preserveSelection] Pass `true` to preserve any other selected events
     * @category Selection
     */
    selectEvent(event, preserveSelection = false) {
      if (!this.isEventSelected(event)) {
        this.selectEvents([event], preserveSelection);
      }
    }
    /**
     * Selects the passed {@link Scheduler.model.AssignmentModel assignment} *if it is not selected*.
     * @param {Scheduler.model.AssignmentModel} assignment The assignment to select
     * @param {Boolean} [preserveSelection] Pass `true` to preserve any other selected assignments
     * @param {Event} [event] If this method was invoked as a result of a user action, this is the DOM event that triggered it
     * @category Selection
     */
    selectAssignment(assignment, preserveSelection = false, event) {
      if (!this.isAssignmentSelected(assignment)) {
        preserveSelection ? this.selectedCollection.add(assignment) : this.selectedAssignments = assignment;
      }
    }
    /**
     * Deselects the passed {@link Scheduler.model.EventModel event} or {@link Scheduler.model.AssignmentModel assignment}
     * *if it is selected*.
     * @param {Scheduler.model.EventModel|Scheduler.model.AssignmentModel} eventOrAssignment The event or assignment to deselect.
     * @category Selection
     */
    deselect(eventOrAssignment) {
      if (eventOrAssignment.isAssignment) {
        this.deselectAssignment(eventOrAssignment);
      } else {
        this.deselectEvent(eventOrAssignment);
      }
    }
    /**
     * Deselects the passed {@link Scheduler.model.EventModel event} *if it is selected*.
     * @param {Scheduler.model.EventModel} event The event to deselect.
     * @category Selection
     */
    deselectEvent(event) {
      if (this.isEventSelected(event)) {
        this.selectedCollection.remove(...event.assignments);
      }
    }
    /**
     * Deselects the passed {@link Scheduler.model.AssignmentModel assignment} *if it is selected*.
     * @param {Scheduler.model.AssignmentModel} assignment The assignment to deselect
     * @param {Event} [event] If this method was invoked as a result of a user action, this is the DOM event that triggered it
     * @category Selection
     */
    deselectAssignment(assignment) {
      if (this.isAssignmentSelected(assignment)) {
        this.selectedCollection.remove(assignment);
      }
    }
    /**
     * Adds {@link Scheduler.model.EventModel events} to the selection.
     * @param {Scheduler.model.EventModel[]} events Events to be selected
     * @param {Boolean} [preserveSelection] Pass `true` to preserve any other selected events
     * @category Selection
     */
    selectEvents(events, preserveSelection = false) {
      if (preserveSelection) {
        const assignments = events.reduce((assignments2, event) => {
          if (this.isEventSelectable(event) !== false) {
            assignments2.push(...event.assignments);
          }
          return assignments2;
        }, []);
        this.selectedCollection.add(assignments);
      } else {
        this.selectedEvents = events;
      }
    }
    /**
     * Removes {@link Scheduler.model.EventModel events} from the selection.
     * @param {Scheduler.model.EventModel[]} events Events or assignments  to be deselected
     * @category Selection
     */
    deselectEvents(events) {
      this.selectedCollection.remove(events.reduce((assignments, event) => {
        assignments.push(...event.assignments);
        return assignments;
      }, []));
    }
    /**
     * Adds {@link Scheduler.model.AssignmentModel assignments} to the selection.
     * @param {Scheduler.model.AssignmentModel[]} assignments Assignments to be selected
     * @category Selection
     */
    selectAssignments(assignments) {
      this.selectedCollection.add(assignments);
    }
    /**
     * Removes {@link Scheduler.model.AssignmentModel assignments} from the selection.
     * @param {Scheduler.model.AssignmentModel[]} assignments Assignments  to be deselected
     * @category Selection
     */
    deselectAssignments(assignments) {
      this.selectedCollection.remove(assignments);
    }
    /**
     * Deselects all {@link Scheduler.model.EventModel events} and {@link Scheduler.model.AssignmentModel assignments}.
     * @category Selection
     */
    clearEventSelection() {
      this.selectedAssignments = [];
    }
    //endregion
    //region Events
    /**
     * Responds to mutations of the underlying selection Collection.
     * Keeps the UI synced, eventSelectionChange and assignmentSelectionChange event is fired when `me.silent` is falsy.
     * @private
     */
    onBeforeSelectedCollectionSplice({ toAdd, toRemove, index }) {
      const me = this, selection = me._selectedCollection.values, selected = toAdd, deselected = toRemove > 0 ? selection.slice(index, toRemove + index) : [], action = me.getActionType(selection, selected, deselected, true);
      if (toAdd.length === 0 && toRemove === 0) {
        return;
      }
      if (me.trigger("beforeEventSelectionChange", {
        action,
        selection: me.getEventsFromAssignments(selection) || [],
        selected: me.getEventsFromAssignments(selected) || [],
        deselected: me.getEventsFromAssignments(deselected) || []
      }) === false) {
        return false;
      }
      if (me.trigger("beforeAssignmentSelectionChange", {
        action,
        selection,
        selected,
        deselected
      }) === false) {
        return false;
      }
    }
    onSelectedCollectionChange({ added, removed }) {
      const me = this, selection = me.selectedAssignments, selected = added || [], deselected = removed || [];
      function updateSelection(assignmentRecord, select) {
        const eventRecord = assignmentRecord.event;
        if (eventRecord) {
          const returnWrapper = false, checkReleased = true, { eventAssignHighlightCls } = me;
          me.currentOrientation.toggleCls(assignmentRecord, me.eventSelectedCls, select, returnWrapper, checkReleased);
          if (eventAssignHighlightCls) {
            const element = me.getElementFromAssignmentRecord(assignmentRecord, returnWrapper, checkReleased);
            me.getElementsFromEventRecord(eventRecord, null, returnWrapper, checkReleased).forEach((el) => {
              if (el !== element) {
                const otherAssignmentRecord = me.resolveAssignmentRecord(el, checkReleased);
                me.currentOrientation.toggleCls(otherAssignmentRecord, eventAssignHighlightCls, select, returnWrapper, checkReleased);
                if (select && !el.parentElement.isReleased) {
                  el.style.animation = "none";
                  el.offsetHeight;
                  el.style.animation = "";
                }
                el.classList.toggle(eventAssignHighlightCls, select);
              }
            });
          }
        }
      }
      deselected.forEach((record) => updateSelection(record, false));
      selected.forEach((record) => updateSelection(record, true));
      if (me.highlightSuccessors || me.highlightPredecessors) {
        me.highlightLinkedEvents(me.selectedEvents);
      }
      me.$selectedAssignments = selection.map((assignment) => ({
        eventId: assignment.eventId,
        resourceId: assignment.resourceId
      }));
      if (!me.silent) {
        const action = this.getActionType(selection, selected, deselected);
        me.trigger("assignmentSelectionChange", {
          action,
          selection,
          selected,
          deselected
        });
        me.trigger("eventSelectionChange", {
          action,
          selection: me.selectedEvents,
          selected: me.getEventsFromAssignments(selected),
          deselected: me.getEventsFromAssignments(deselected)
        });
      }
    }
    /**
     * Assignment change listener to remove events from selection which are no longer in the assignments.
     * @private
     */
    onAssignmentChange(event) {
      super.onAssignmentChange(event);
      const me = this, { action, records: assignments } = event;
      me.silent = !me.triggerSelectionChangeOnRemove;
      if (action === "remove") {
        me.deselectAssignments(assignments);
      } else if (action === "removeall" && !me.eventStore.isSettingData) {
        me.clearEventSelection();
      } else if (action === "dataset" && me.$selectedAssignments) {
        if (!me.maintainSelectionOnDatasetChange) {
          me.clearEventSelection();
        } else {
          const newAssignments = me.$selectedAssignments.map(
            (selector) => assignments.find(
              (a) => a.eventId === selector.eventId && a.resourceId === selector.resourceId
            )
          );
          me.selectedAssignments = ArrayHelper.clean(newAssignments);
        }
      }
      me.silent = false;
    }
    onInternalEventStoreChange({ source, action, records }) {
      if (!source.isResourceTimeRangeStore && action === "dataset" && !records.length) {
        this.clearEventSelection();
      }
      super.onInternalEventStoreChange(...arguments);
    }
    isMultiEventModifierPressed(event) {
      const { multiEventSelect } = this;
      if (multiEventSelect && typeof multiEventSelect === "object") {
        return multiEventSelect.ctrlKey && event.ctrlKey || multiEventSelect.altKey && event.altKey || multiEventSelect.shiftKey && event.shiftKey;
      }
      return false;
    }
    /**
     * Mouse listener to update selection.
     * @private
     */
    onAssignmentSelectionClick(event, clickedRecord) {
      const me = this;
      if (me.isAssignmentSelected(clickedRecord)) {
        if (me.deselectOnClick || (!me.multiEventSelect && event.ctrlKey || me.isMultiEventModifierPressed(event))) {
          me.deselectAssignment(clickedRecord, me.multiEventSelect, event);
        }
      } else if (this.isEventSelectable(clickedRecord.event) !== false) {
        me.selectAssignment(clickedRecord, me.isMultiEventModifierPressed(event), event);
      }
    }
    /**
     * Navigation listener to update selection.
     * @private
     */
    onEventNavigate({ event, item }) {
      if (!this.eventSelectionDisabled) {
        const assignment = item && (item.nodeType === Element.ELEMENT_NODE ? this.resolveAssignmentRecord(item) : item);
        if (assignment) {
          this.onAssignmentSelectionClick(event, assignment);
        } else if (this.deselectAllOnScheduleClick) {
          this.clearEventSelection();
        }
      }
    }
    changeHighlightSuccessors(value2) {
      return this.changeLinkedEvents(value2);
    }
    changeHighlightPredecessors(value2) {
      return this.changeLinkedEvents(value2);
    }
    changeLinkedEvents(value2) {
      const me = this;
      if (value2) {
        me.highlighted = me.highlighted || /* @__PURE__ */ new Set();
        me.highlightLinkedEvents(me.selectedEvents);
      } else if (me.highlighted) {
        me.highlightLinkedEvents();
      }
      return value2;
    }
    // Function that highlights/unhighlights events in a dependency chain
    highlightLinkedEvents(eventRecords = []) {
      const me = this, {
        highlighted,
        eventStore
      } = me, dependenciesFeature = me.features.dependencies;
      highlighted.forEach((eventRecord) => {
        if (!eventRecords.includes(eventRecord)) {
          eventRecord.meta.highlight = false;
          highlighted.delete(eventRecord);
          if (eventStore.includes(eventRecord)) {
            eventRecord.dependencies.forEach((dep) => dependenciesFeature.unhighlight(dep, "b-highlight"));
          }
        }
      });
      eventRecords.forEach((eventRecord) => {
        const toWalk = [eventRecord];
        while (toWalk.length) {
          const record = toWalk.pop();
          highlighted.add(record);
          if (me.highlightSuccessors) {
            record.outgoingDeps.forEach((outgoing) => {
              dependenciesFeature.highlight(outgoing, "b-highlight");
              !highlighted.has(outgoing.toEvent) && toWalk.push(outgoing.toEvent);
            });
          }
          if (me.highlightPredecessors) {
            record.incomingDeps.forEach((incoming) => {
              dependenciesFeature.highlight(incoming, "b-highlight");
              !highlighted.has(incoming.fromEvent) && toWalk.push(incoming.fromEvent);
            });
          }
        }
        highlighted.forEach((record) => record.meta.highlight = true);
      });
      me.element.classList.toggle("b-highlighting", eventRecords.length > 0);
      me.refreshWithTransition();
    }
    onEventDataGenerated(renderData) {
      if (this.highlightSuccessors || this.highlightPredecessors) {
        renderData.cls["b-highlight"] = renderData.eventRecord.meta.highlight;
      }
      super.onEventDataGenerated(renderData);
    }
    updateProject(project, old) {
      this.clearEventSelection();
      super.updateProject(project, old);
    }
    //endregion
    //region Getters/Setters
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
    //endregion
  }, __publicField(_a5, "$name", "EventSelection"), _a5;
};

// ../Scheduler/lib/Scheduler/view/mixin/EventNavigation.js
var preventDefault2 = (e) => e.preventDefault();
var isArrowKey = {
  ArrowRight: 1,
  ArrowLeft: 1,
  ArrowUp: 1,
  ArrowDown: 1
};
var animate100 = {
  animate: 100
};
var emptyObject12 = Object.freeze({});
var sortAssignmentsAsc = ({ event: { startDate: lhs } }, { event: { startDate: rhs } }) => lhs - rhs;
var sortAssignmentsDesc = ({ event: { startDate: lhs } }, { event: { startDate: rhs } }) => rhs - lhs;
var EventNavigation_default = (Target) => {
  var _a5;
  return _a5 = class extends Delayable_default(Target || Base) {
    //endregion
    //region Events
    /**
     * Fired when a user gesture causes the active item to change.
     * @event navigate
     * @param {Event} event The browser event which instigated navigation. May be a click or key or focus event.
     * @param {HTMLElement|null} item The newly active item, or `null` if focus moved out.
     * @param {HTMLElement|null} oldItem The previously active item, or `null` if focus is moving in.
     */
    //endregion
    construct(config) {
      const me = this;
      super.construct(config);
      me.isInTimeAxis = me.isInTimeAxis.bind(me);
      me.onDeleteKey = me.throttle(me.onDeleteKey, me.onDeleteKeyBuffer, me);
    }
    changeNavigator(navigator2) {
      const me = this, { isVertical } = me;
      me.getConfig("subGridConfigs");
      if (isVertical) {
        navigator2.prevKey = "ArrowUp";
        navigator2.nextKey = "ArrowDown";
        navigator2.keys.ArrowUp = navigator2.keys.ArrowDown = null;
        navigator2.keys.ArrowLeft = navigator2.keys.ArrowRight = "navigateResourceHeaderCells";
      }
      return new Navigator(me.constructor.mergeConfigs({
        ownerCmp: me,
        target: isVertical ? me.bodyElement : me.timeAxisSubGridElement,
        processEvent: me.processEvent,
        itemSelector: `.${me.eventCls}-wrap${isVertical ? ",.b-resourceheader-cell" : ""}`,
        focusCls: me.focusCls,
        navigatePrevious: me.throttle(me.navigatePrevious, { delay: me.navigatePreviousBuffer, throttled: preventDefault2 }),
        navigateNext: me.throttle(me.navigateNext, { delay: me.navigateNextBuffer, throttled: preventDefault2 })
      }, navigator2));
    }
    doDestroy() {
      this.navigator.destroy();
      super.doDestroy();
    }
    /**
     * Determines if an event is within the time axis.
     *
     * @param {Scheduler.model.EventModel} eventRecord The event to check
     * @returns {Boolean} `true` if the record is within the time axis
     */
    isInTimeAxis(eventRecord) {
      return !eventRecord.instanceMeta(this).excluded && this.timeAxis.isTimeSpanInAxis(eventRecord);
    }
    onElementKeyDown(keyEvent) {
      var _a6, _b2, _c2;
      const me = this, { navigator: navigator2 } = me;
      if (((_a6 = me.focusedCell) == null ? void 0 : _a6.rowIndex) !== -1 && ((_b2 = me.focusedCell) == null ? void 0 : _b2.column) === me.timeAxisColumn && !keyEvent.target.closest(navigator2.itemSelector) && keyEvent.key === "Enter") {
        const firstAssignment = me.getFirstVisibleAssignment();
        if (firstAssignment) {
          me.navigateTo(firstAssignment, {
            uiEvent: keyEvent
          });
          return false;
        }
      } else {
        (_c2 = super.onElementKeyDown) == null ? void 0 : _c2.call(this, keyEvent);
      }
    }
    getFirstVisibleAssignment(location2 = this.focusedCell) {
      var _a6, _b2, _c2;
      const me = this, {
        currentOrientation,
        rowManager,
        eventStore
      } = me;
      if (me.isHorizontal) {
        let renderedEvents = currentOrientation.rowMap.get(rowManager.getRow(location2.rowIndex));
        if (renderedEvents == null ? void 0 : renderedEvents.length) {
          return (_a6 = renderedEvents[0]) == null ? void 0 : _a6.elementData.assignmentRecord;
        } else {
          renderedEvents = (_b2 = currentOrientation.resourceMap.get(location2.id)) == null ? void 0 : _b2.eventsData;
          if (renderedEvents == null ? void 0 : renderedEvents.length) {
            return (_c2 = renderedEvents.filter((e) => eventStore.isAvailable(e.eventRecord))[0]) == null ? void 0 : _c2.assignmentRecord;
          }
        }
      } else {
        const firstResource = [...currentOrientation.resourceMap.values()][0], renderedEvents = firstResource && Object.values(firstResource);
        if (renderedEvents == null ? void 0 : renderedEvents.length) {
          return renderedEvents.filter((e) => eventStore.isAvailable(e.renderData.eventRecord))[0].renderData.assignmentRecord;
        }
      }
    }
    onGridBodyFocusIn(focusEvent) {
      var _a6;
      const isGridCellFocus = focusEvent.target.closest(this.focusableSelector);
      if (this.timeAxisSubGridElement.contains(focusEvent.target)) {
        const me = this, { navigationEvent } = me, { target } = focusEvent, eventFocus = target.closest(me.navigator.itemSelector), destinationCell = eventFocus ? me.normalizeCellContext({
          rowIndex: me.isVertical ? 0 : me.resourceStore.indexOf(me.resolveResourceRecord(target)),
          column: me.timeAxisColumn,
          target
        }) : new GridLocation(target);
        if (eventFocus) {
          const { _focusedCell } = me;
          me._focusedCell = destinationCell;
          (_a6 = me.onCellNavigate) == null ? void 0 : _a6.call(me, me, _focusedCell, destinationCell, navigationEvent, true);
          return;
        }
        if (isGridCellFocus && (!navigationEvent || isArrowKey[navigationEvent.key])) {
          const firstAssignment = me.getFirstVisibleAssignment(destinationCell);
          if (firstAssignment) {
            me.navigateTo(firstAssignment, {
              // Only change scroll if focus came from key press
              scrollIntoView: Boolean(navigationEvent && navigationEvent.type !== "mousedown"),
              uiEvent: navigationEvent || focusEvent
            });
            return;
          }
        }
      }
      if (isGridCellFocus) {
        super.onGridBodyFocusIn(focusEvent);
      }
    }
    /*
     * Override of GridNavigation#focusCell method to handle the TimeAxisColumn.
     * Not needed until we implement full keyboard accessibility.
     */
    accessibleFocusCell(cellSelector, options) {
      const me = this;
      cellSelector = me.normalizeCellContext(cellSelector);
      if (cellSelector.columnId !== me.timeAxisColumn.id) {
        return super.focusCell(cellSelector, options);
      }
    }
    // Interface method to extract the navigated to record from a populated 'navigate' event.
    // Gantt, Scheduler and Calendar handle event differently, adding different properties to it.
    // This method is meant to be overridden to return correct target from event
    normalizeTarget(event) {
      return event.assignmentRecord;
    }
    getPrevious(assignmentRecord, isDelete) {
      const me = this, { resourceStore, visibleDateRange } = me, { eventSorter } = me.currentOrientation, { startDate, endDate } = me.timeAxis, eventRecord = assignmentRecord.event, resourceEvents = me.eventStore.getEvents({
        resourceRecord: assignmentRecord.resource,
        startDate,
        endDate,
        visibleDateRange
      }).filter(this.isInTimeAxis).sort(eventSorter);
      let resourceRecord = assignmentRecord.resource, previousEvent = resourceEvents[resourceEvents.indexOf(eventRecord) - 1];
      if (!previousEvent) {
        for (let rowIdx = resourceStore.indexOf(resourceRecord) - 1; (!previousEvent || isDelete && previousEvent === eventRecord) && rowIdx >= 0; rowIdx--) {
          resourceRecord = resourceStore.getAt(rowIdx);
          const events = me.eventStore.getEvents({
            resourceRecord,
            startDate,
            endDate
          }).filter(me.isInTimeAxis).sort(eventSorter);
          previousEvent = events.length && events[events.length - 1];
        }
      }
      return me.assignmentStore.getAssignmentForEventAndResource(previousEvent, resourceRecord);
    }
    // Invoked only in vertical mode when navigating resource header cells
    navigateResourceHeaderCells(keyEvent) {
      const { target } = keyEvent;
      if (target.closest(".b-resourceheader-cell")) {
        const targetResource = this.resourceStore[keyEvent.key === "ArrowRight" ? "getNext" : "getPrev"](this.resolveResourceRecord(target)), toFocus = target.parentElement.querySelector(`[data-resource-id="${targetResource == null ? void 0 : targetResource.id}"]`);
        if (toFocus) {
          keyEvent.handled = true;
          keyEvent.preventDefault();
          toFocus.scrollIntoView();
          toFocus.focus();
        }
      }
    }
    navigatePrevious(keyEvent) {
      const me = this, previousAssignment = keyEvent.target.closest(me.eventSelector) && me.getPrevious(me.normalizeTarget(keyEvent));
      if (previousAssignment) {
        keyEvent.preventDefault();
        if (!keyEvent.ctrlKey) {
          me.clearEventSelection();
        }
        return me.navigateTo(previousAssignment, {
          uiEvent: keyEvent
        });
      }
      keyEvent.handled = false;
      return me.doGridNavigation(keyEvent);
    }
    getNext(assignmentRecord, isDelete) {
      const me = this, { resourceStore, visibleDateRange } = me, { eventSorter } = me.currentOrientation, { startDate, endDate } = me.timeAxis, eventRecord = assignmentRecord.event, resourceEvents = me.eventStore.getEvents({
        resourceRecord: assignmentRecord.resource,
        // start/end are required to limit time
        startDate,
        endDate,
        visibleDateRange
      }).filter(this.isInTimeAxis).sort(eventSorter);
      let resourceRecord = assignmentRecord.resource, nextEvent = resourceEvents[resourceEvents.indexOf(eventRecord) + 1];
      if (!nextEvent) {
        for (let rowIdx = resourceStore.indexOf(resourceRecord) + 1; (!nextEvent || isDelete && nextEvent === eventRecord) && rowIdx < resourceStore.count; rowIdx++) {
          resourceRecord = resourceStore.getAt(rowIdx);
          const events = me.eventStore.getEvents({
            resourceRecord,
            startDate,
            endDate
          }).filter(me.isInTimeAxis).sort(eventSorter);
          nextEvent = events[0];
        }
      }
      return me.assignmentStore.getAssignmentForEventAndResource(nextEvent, resourceRecord);
    }
    navigateNext(keyEvent) {
      const me = this, { target } = keyEvent, nextAssignment = target.closest(me.eventSelector) && me.getNext(me.normalizeTarget(keyEvent));
      if (nextAssignment) {
        keyEvent.preventDefault();
        if (!keyEvent.ctrlKey) {
          me.clearEventSelection();
        }
        return me.navigateTo(nextAssignment, {
          uiEvent: keyEvent
        });
      }
      if (me.isVertical && target.closest(".b-resourceheader-cell")) {
        const resource = me.resolveResourceRecord(target), { visibleDateRange } = me, { eventSorter } = me.currentOrientation, { startDate, endDate } = me.timeAxis, resourceEvents = me.eventStore.getEvents({
          resourceRecord: resource,
          filter: me.isInTimeAxis,
          // start/end are required to limit time
          startDate,
          endDate,
          visibleDateRange
        }).sort(eventSorter), nextEvent = resourceEvents[0], nextAssignment2 = nextEvent && me.assignmentStore.getAssignmentForEventAndResource(nextEvent, resource);
        if (nextAssignment2) {
          return me.navigateTo(nextAssignment2, {
            uiEvent: keyEvent
          });
        }
        return me.navigateDown();
      } else {
        keyEvent.handled = false;
        return me.doGridNavigation(keyEvent);
      }
    }
    doGridNavigation(keyEvent) {
      var _a6;
      if (!keyEvent.handled && keyEvent.key.indexOf("Arrow") === 0) {
        (_a6 = this[`navigate${keyEvent.key.substring(5)}ByKey`]) == null ? void 0 : _a6.call(this, keyEvent);
      }
    }
    async navigateTo(targetAssignment, {
      scrollIntoView = true,
      uiEvent = {}
    } = emptyObject12) {
      const me = this, { navigator: navigator2 } = me, { skipScrollIntoView } = navigator2;
      if (targetAssignment) {
        if (scrollIntoView) {
          navigator2.disabled = true;
          await me.scrollAssignmentIntoView(targetAssignment, {
            extendTimeAxis: me.infiniteScroll,
            ...animate100
          });
          navigator2.disabled = false;
        } else {
          navigator2.skipScrollIntoView = true;
        }
        if (!me.isDestroyed && this.getElementFromAssignmentRecord(targetAssignment)) {
          me.activeAssignment = targetAssignment;
          navigator2.skipScrollIntoView = skipScrollIntoView;
          navigator2.trigger("navigate", {
            event: uiEvent,
            item: me.getElementFromAssignmentRecord(targetAssignment).closest(navigator2.itemSelector)
          });
        }
      }
    }
    set activeAssignment(assignmentRecord) {
      const assignmentEl = this.getElementFromAssignmentRecord(assignmentRecord, true);
      if (assignmentEl) {
        this.navigator.activeItem = assignmentEl;
      }
    }
    get activeAssignment() {
      const { activeItem } = this.navigator;
      if (activeItem) {
        return this.resolveAssignmentRecord(activeItem);
      }
    }
    get previousActiveEvent() {
      const { previousActiveItem } = this.navigator;
      if (previousActiveItem) {
        return this.resolveEventRecord(previousActiveItem);
      }
    }
    processEvent(keyEvent) {
      const me = this, eventElement = keyEvent.target.closest(me.eventSelector);
      if (eventElement) {
        keyEvent.assignmentRecord = me.resolveAssignmentRecord(eventElement);
        keyEvent.eventRecord = me.resolveEventRecord(eventElement);
        keyEvent.resourceRecord = me.resolveResourceRecord(eventElement);
      }
      return keyEvent;
    }
    onTab(event) {
      if (event.target.closest(this.eventSelector)) {
        this.navigateNext(this.processEvent(event));
      }
      if (!event.defaultPrevented) {
        super.onTab(...arguments);
      }
    }
    onShiftTab(event) {
      if (event.target.closest(this.eventSelector)) {
        this.navigatePrevious(this.processEvent(event));
      }
      if (!event.defaultPrevented) {
        super.onShiftTab(...arguments);
      }
    }
    onDeleteKey(keyEvent) {
      const me = this;
      if (!me.readOnly && me.enableDeleteKey) {
        const records = me.eventStore.usesSingleAssignment ? me.selectedEvents : me.selectedAssignments;
        me.removeEvents(records.filter((record) => {
          var _a6;
          return !record.readOnly && !((_a6 = record.event) == null ? void 0 : _a6.readOnly);
        }));
      }
    }
    onArrowUpKey(keyEvent) {
      this.focusCell({
        rowIndex: this.focusedCell.rowIndex - 1,
        column: this.timeAxisColumn
      });
      keyEvent.handled = true;
    }
    onArrowDownKey(keyEvent) {
      if (this.focusedCell.rowIndex < this.resourceStore.count - 1) {
        this.focusCell({
          rowIndex: this.focusedCell.rowIndex + 1,
          column: this.timeAxisColumn
        });
        keyEvent.handled = true;
      }
    }
    navigateLeft(keyEvent) {
      if (this.isVertical && this.activeAssignment) {
        const me = this, { activeAssignment, eventStore, resourceStore } = me, { resource } = activeAssignment, { startDate, endDate } = activeAssignment.event;
        let assignments = eventStore.getAssignmentsForResource(resource).filter(({ event: { startDate: s, endDate: e } }) => DateHelper.intersectSpans(s, e, startDate, endDate) && s < startDate);
        if (!assignments.length) {
          for (let r = resourceStore.indexOf(resource) - 1; !assignments.length && r >= 0; r--) {
            assignments = me.eventStore.getAssignmentsForResource(resourceStore.getAt(r));
          }
        }
        if (assignments.length) {
          return me.navigateTo(assignments.sort(sortAssignmentsDesc)[0], {
            uiEvent: keyEvent
          });
        }
        return super.navigateLeft(keyEvent);
      } else {
        return super.navigateLeft(keyEvent);
      }
    }
    navigateRight(keyEvent) {
      if (this.isVertical && this.activeAssignment) {
        const me = this, { activeAssignment, eventStore, resourceStore } = me, { resource } = activeAssignment, { startDate, endDate } = activeAssignment.event;
        let assignments = eventStore.getAssignmentsForResource(resource).filter((a) => a !== activeAssignment).filter(({ event: { startDate: s, endDate: e } }) => DateHelper.intersectSpans(s, e, startDate, endDate) && s >= startDate);
        if (!assignments.length) {
          for (let r = resourceStore.indexOf(resource) + 1; !assignments.length && r < resourceStore.count; r++) {
            assignments = this.eventStore.getAssignmentsForResource(resourceStore.getAt(r));
          }
        }
        if (assignments.length) {
          return me.navigateTo(assignments.sort(sortAssignmentsAsc)[0], {
            uiEvent: keyEvent
          });
        }
        return super.navigateRight(keyEvent);
      } else {
        return super.navigateRight(keyEvent);
      }
    }
    onEscapeKey(keyEvent) {
      if (!keyEvent.target.closest(".b-dragging")) {
        this.focusCell({
          rowIndex: this.focusedCell.rowIndex,
          column: this.timeAxisColumn
        }, {
          disableActionable: true,
          scroll: false
        });
        keyEvent.handled = true;
      }
    }
    onEventSpaceKey(keyEvent) {
    }
    onEventEnterKey(keyEvent) {
    }
    get isActionableLocation() {
      if (!this.navigator.activeItem) {
        return super.isActionableLocation;
      }
    }
    onNavigate({ item, event }) {
      var _a6;
      if ((_a6 = item == null ? void 0 : item.elementData) == null ? void 0 : _a6.ariaLabel) {
        item.setAttribute("aria-label", item.elementData.ariaLabel);
      }
    }
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "EventNavigation"), //region Default config
  __publicField(_a5, "configurable", {
    /**
     * A config object to use when creating the {@link Core.helper.util.Navigator}
     * to use to perform keyboard navigation in the timeline.
     * @config {NavigatorConfig}
     * @default
     * @category Misc
     * @internal
     */
    navigator: {
      allowCtrlKey: true,
      scrollSilently: true,
      keys: {
        Space: "onEventSpaceKey",
        Enter: "onEventEnterKey",
        Delete: "onDeleteKey",
        Backspace: "onDeleteKey",
        ArrowUp: "onArrowUpKey",
        ArrowDown: "onArrowDownKey",
        Escape: "onEscapeKey"
      },
      internalListeners: {
        navigate: "up.onNavigate"
      }
    },
    isNavigationKey: {
      ArrowDown: 1,
      ArrowUp: 1,
      ArrowLeft: 1,
      ArrowRight: 1
    }
  }), __publicField(_a5, "defaultConfig", {
    /**
     * A CSS class name to add to focused events.
     * @config {String}
     * @default
     * @category CSS
     * @private
     */
    focusCls: "b-active",
    /**
     * Allow using [Delete] and [Backspace] to remove events/assignments
     * @config {Boolean}
     * @default
     * @category Misc
     */
    enableDeleteKey: true,
    // Number in milliseconds to buffer handlers execution. See `Delayable.throttle` function docs.
    onDeleteKeyBuffer: 500,
    navigatePreviousBuffer: 200,
    navigateNextBuffer: 200,
    testConfig: {
      onDeleteKeyBuffer: 1
    }
  }), _a5;
};

// ../Scheduler/lib/Scheduler/view/mixin/TransactionalFeatureMixin.js
var TransactionalFeatureMixin_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    get widgetClass() {
    }
    /**
     * Returns `true` if queue is supported and enabled
     * @member {Boolean}
     * @internal
     * @readonly
     */
    get transactionalFeaturesEnabled() {
      return this.enableTransactionalFeatures && this.project && Boolean(this.project._queue);
    }
    get isTransactionalFeatureActive() {
      return this._featureTransactionId != null;
    }
    get featureTransactionId() {
      return this._featureTransactionId;
    }
    construct(...config) {
      super.construct(...config);
      this.ion({
        beforeFeatureTransactionStart: ({ id: id2 }) => this._featureTransactionId = id2,
        featureTransactionComplete: () => delete this._featureTransactionId
      });
    }
    afterFeatureTransaction(callback) {
      if (!this.transactionalFeaturesEnabled) {
        return;
      }
      if (this.isTransactionalFeatureActive) {
        this.once("featureTransactionComplete", callback);
      } else {
        callback.call(this);
      }
    }
  }, __publicField(_a5, "$name", "TransactionalFeatureMixin"), __publicField(_a5, "configurable", {
    /**
     * When true, some features will start a project transaction, blocking the project queue, suspending
     * store events and preventing UI from updates. It behaves similar to
     * {@link Grid.column.Column#config-instantUpdate} set to `false`.
     * Set `false` to not use project queue.
     * @config {Boolean}
     * @default
     */
    enableTransactionalFeatures: false,
    testConfig: {
      enableTransactionalFeatures: false
    }
  }), _a5;
};

// ../Scheduler/lib/Scheduler/crud/mixin/CrudManagerView.js
var CrudManagerView_default = (Target) => {
  var _a5;
  return _a5 = class extends Target.mixin(LoadMaskable_default) {
    //region Init
    afterConstruct() {
      super.afterConstruct();
      const { crudManager, project } = this;
      if (this.loadMask && (crudManager || project).isCrudManagerLoading) {
        this.onCrudManagerLoadStart();
      }
    }
    //endregion
    /**
     * Applies the {@link Scheduler.crud.mixin.CrudManagerView#config-syncMask} as the
     * {@link Core.widget.Widget#config-masked mask} for this widget.
     * @internal
     */
    applySyncMask() {
      const { syncMask } = this;
      if (syncMask) {
        this.masked = Mask.mergeConfigs(this.loadMaskDefaults, syncMask);
      }
    }
    /**
     * Hooks up crud manager listeners
     * @private
     * @category Store
     */
    bindCrudManager(crudManager) {
      this.detachListeners("crudManager");
      let additionalListeners = {};
      if (!this.isTaskBoardBase) {
        additionalListeners = {
          beforeApplyResponse: "onCrudManagerBeforeApplyResponse",
          applyResponse: "onCrudManagerApplyResponse",
          beforeLoadCrudManagerData: "onCrudManagerBeforeLoadCrudManagerData",
          loadCrudManagerData: "onCrudManagerLoadCrudManagerData"
        };
      }
      crudManager == null ? void 0 : crudManager.ion({
        name: "crudManager",
        loadStart: "onCrudManagerLoadStart",
        beforeSend: "onCrudManagerBeforeSend",
        load: "onCrudManagerLoad",
        loadCanceled: "onCrudManagerLoadCanceled",
        syncStart: "onCrudManagerSyncStart",
        sync: "onCrudManagerSync",
        syncCanceled: "onCrudManagerSyncCanceled",
        requestFail: "onCrudManagerRequestFail",
        requestDone: "onAjaxTransportResponseDone",
        ...additionalListeners,
        thisObj: this
      });
    }
    onCrudManagerBeforeSend({ params }) {
      var _a6;
      (_a6 = this.applyStartEndParameters) == null ? void 0 : _a6.call(this, params);
    }
    onCrudManagerLoadStart() {
      var _a6;
      this.applyLoadMask();
      (_a6 = this.toggleEmptyText) == null ? void 0 : _a6.call(this);
    }
    onCrudManagerSyncStart() {
      this.applySyncMask();
    }
    onCrudManagerBeforeApplyResponse() {
      this.suspendRefresh();
    }
    onCrudManagerApplyResponse({ request }) {
      this.resumeRefresh(true, request.type === "sync");
    }
    onCrudManagerBeforeLoadCrudManagerData() {
      if (!this.crudManager.applyingLoadResponse) {
        this.suspendRefresh();
      }
    }
    async onCrudManagerLoadCrudManagerData() {
      if (!this.crudManager.applyingLoadResponse) {
        await this.project.commitAsync();
        !this.isDestroyed && this.resumeRefresh(true);
      }
    }
    onCrudManagerRequestFinalize(successful = true, requestType, response) {
      var _a6;
      const me = this;
      if (successful) {
        (_a6 = me.toggleEmptyText) == null ? void 0 : _a6.call(me);
      } else {
        if (!me.masked) {
          me.applyLoadMask();
        }
        me.applyMaskError(
          `<div class="b-grid-load-failure">
                    <div class="b-grid-load-fail">${me.L(`L{GridBase.${requestType}FailedMessage}`)}</div>
                    ${response && response.message ? `<div class="b-grid-load-fail">${me.L("L{CrudManagerView.serverResponseLabel}")} ${response.message}</div>` : ""}
                </div>`
        );
      }
    }
    onCrudManagerLoadCanceled() {
      this.onCrudManagerRequestFinalize(true, "load");
    }
    onCrudManagerSyncCanceled() {
      this.onCrudManagerRequestFinalize(true, "sync");
    }
    onCrudManagerLoad() {
      this.onCrudManagerRequestFinalize(true, "load");
    }
    onCrudManagerSync() {
      this.onCrudManagerRequestFinalize(true, "sync");
    }
    onCrudManagerRequestFail({ requestType, response }) {
      this.onCrudManagerRequestFinalize(false, requestType, response);
    }
    onAjaxTransportResponseDone() {
      const me = this;
      if (me.clearMaskDelay != null) {
        me.setTimeout(() => me.masked = null, me.clearMaskDelay);
      } else {
        me.masked = null;
      }
    }
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "CrudManagerView"), __publicField(_a5, "config", {
    clearMaskDelay: null,
    // Test environment may be in a poll wait for mask to disappear.
    // Hiding the mask immediately, before the load sequence ends releases it too early
    testConfig: {
      clearMaskDelay: 0
    }
  }), _a5;
};

// ../Scheduler/lib/Scheduler/data/mixin/LazyLoadView.js
var LazyLoadView_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    construct(config) {
      var _a6;
      const me = this;
      me.$lazyLoadDetachers = /* @__PURE__ */ new Map();
      super.construct(config);
      if ((_a6 = me.crudManager) == null ? void 0 : _a6.lazyLoad) {
        let targetClass = me.resourceStore.lazyLoad, targetMethod = "doLazyLoad";
        if (me.crudManager.remotePaging) {
          targetClass = me.resourceStore;
          targetMethod = "requestData";
        }
        me.$lazyLoadDetachers.set(me, FunctionHelper.before(targetClass, targetMethod, "beforeResourceStoreLoad", me));
        me.passStartEndParameters = false;
      }
    }
    beforeResourceStoreLoad(params) {
      const me = this, range = {
        startDate: me.startDate,
        endDate: me.endDate,
        visibleStart: me.visibleDateRange.startDate,
        visibleEnd: me.visibleDateRange.endDate
      };
      if (me.crudManager.remotePaging) {
        const pageParam = me.resourceStore.defaultPageParamName, pageSizeParam = me.resourceStore.pageSizeParamName;
        range[pageParam] = params[pageParam];
        range[pageSizeParam] = params[pageSizeParam];
      }
      const dateRange = me.eventStore.lazyLoad.calculateDateRange(range);
      Object.assign(params, dateRange);
    }
    updateEventStore(store, oldStore) {
      var _a6;
      (_a6 = super.updateEventStore) == null ? void 0 : _a6.call(this, store, oldStore);
      this.lazyLoadAttachStore(store, oldStore);
      if (store.lazyLoad) {
        this.passStartEndParameters = false;
      }
    }
    updateResourceTimeRangeStore(store, oldStore) {
      var _a6;
      (_a6 = super.updateResourceTimeRangeStore) == null ? void 0 : _a6.call(this, store, oldStore);
      this.lazyLoadAttachStore(store, oldStore);
    }
    updateAssignmentStore(store, oldStore) {
      var _a6;
      (_a6 = super.updateAssignmentStore) == null ? void 0 : _a6.call(this, store, oldStore);
      this.lazyLoadAttachStore(store, oldStore);
    }
    updateTimeRangeStore(store, oldStore) {
      var _a6;
      (_a6 = super.updateTimeRangeStore) == null ? void 0 : _a6.call(this, store, oldStore);
      this.lazyLoadAttachStore(store, oldStore);
    }
    lazyLoadAttachStore(store, oldStore) {
      var _a6, _b2, _c2;
      const me = this, { $lazyLoadDetachers } = me;
      if (oldStore) {
        (_a6 = $lazyLoadDetachers.get(oldStore)) == null ? void 0 : _a6();
        $lazyLoadDetachers.delete(oldStore);
        if (oldStore.lazyLoad.beforeLoadCall) {
          oldStore.lazyLoad.beforeLoadCall = null;
        }
      }
      if (store && ((_b2 = me.resourceStore) == null ? void 0 : _b2.isPaged)) {
        if (store.lazyLoad && !store.isPaged) {
          store.remotePaging = true;
        } else if (store.isPaged && !store.lazyLoad) {
          store.lazyLoad = true;
        }
      }
      if (store == null ? void 0 : store.lazyLoad) {
        (_c2 = $lazyLoadDetachers.get(store)) == null ? void 0 : _c2();
        $lazyLoadDetachers.set(store, store.ion({
          lazyLoadStarted: "storeLazyLoadStarted",
          lazyLoadEnded: "storeLazyLoadEnded",
          thisObj: me
        }));
        if (store.lazyLoad.isDateStoreLazyLoadPlugin) {
          store.lazyLoad.beforeLoadCall = (params) => {
            Object.assign(params, {
              startDate: me.startDate,
              endDate: me.endDate,
              visibleStart: me.visibleDateRange.startDate,
              visibleEnd: me.visibleDateRange.endDate,
              resourceRecord: me.resourceStore.first
            });
          };
        }
      }
    }
    onCrudManagerLoadStart({ source, pack }) {
      if (source.lazyLoad && !pack.stores.includes("resources")) {
        return;
      }
      return super.onCrudManagerLoadStart(...arguments);
    }
    storeLazyLoadStarted({ source }) {
      var _a6;
      const stores2 = (_a6 = this.$storesLazyLoading) != null ? _a6 : this.$storesLazyLoading = /* @__PURE__ */ new Set();
      if (!stores2.size) {
        this.lazyLoadShowLoadingIndicator();
      }
      stores2.add(source);
    }
    storeLazyLoadEnded({ source }) {
      const { $storesLazyLoading } = this;
      $storesLazyLoading.delete(source);
      if (!$storesLazyLoading.size) {
        this.lazyLoadHideLoadingIndicator();
      }
    }
    // Shows a loading indicator inside the main header row
    lazyLoadShowLoadingIndicator() {
      const me = this;
      if (me.lazyLoadingIndicator !== false) {
        if (!me.$lazyLoadingIndicatorElement) {
          me.$lazyLoadingIndicatorElement = DomHelper.createElement({
            parent: me.timeAxisSubGrid.header.element,
            retainElement: true,
            className: "b-timeline-loading-indicator-wrap",
            children: [
              {
                className: "b-timeline-loading-indicator"
              }
            ]
          });
        } else {
          me.timeAxisSubGrid.header.element.appendChild(me.$lazyLoadingIndicatorElement);
        }
      }
      me.readOnly = true;
    }
    lazyLoadHideLoadingIndicator() {
      var _a6;
      (_a6 = this.$lazyLoadingIndicatorElement) == null ? void 0 : _a6.remove();
      this.readOnly = false;
    }
    doDestroy() {
      var _a6;
      (_a6 = this.$lazyLoadDetachers) == null ? void 0 : _a6.forEach((detacher2) => detacher2());
      super.doDestroy();
    }
  }, __publicField(_a5, "$name", "LazyLoadView"), _a5;
};

// ../Scheduler/lib/Scheduler/data/mixin/AttachToProjectMixin.js
var AttachToProjectMixin_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    async afterConstruct() {
      var _a6;
      super.afterConstruct();
      const me = this, projectHolder = me.client || me.grid, { project } = projectHolder;
      (_a6 = projectHolder.projectSubscribers) == null ? void 0 : _a6.push(me);
      if (project) {
        me.attachToProject(project);
        me.attachToResourceStore(project.resourceStore);
        me.attachToEventStore(project.eventStore);
        me.attachToAssignmentStore(project.assignmentStore);
        me.attachToDependencyStore(project.dependencyStore);
        me.attachToCalendarManagerStore(project.calendarManagerStore);
      }
    }
    /**
     * Override to take action when the project instance is replaced.
     *
     * @param {Scheduler.model.ProjectModel} project
     */
    attachToProject(project) {
      var _a6;
      this.detachListeners("project");
      this._project = project;
      (_a6 = super.attachToProject) == null ? void 0 : _a6.call(this, project);
    }
    detachFromProject(project = this._project) {
      var _a6;
      this.detachListeners("project");
      (_a6 = super.detachFromProject) == null ? void 0 : _a6.call(this, project);
    }
    /**
     * Override to take action when the EventStore instance is replaced, either from being replaced on the project or
     * from assigning a new project.
     *
     * @param {Scheduler.data.EventStore} store
     */
    attachToEventStore(store) {
      var _a6;
      this.detachListeners("eventStore");
      (_a6 = super.attachToEventStore) == null ? void 0 : _a6.call(this, store);
    }
    /**
     * Override to take action when the ResourceStore instance is replaced, either from being replaced on the project
     * or from assigning a new project.
     *
     * @param {Scheduler.data.ResourceStore} store
     */
    attachToResourceStore(store) {
      var _a6;
      this.detachListeners("resourceStore");
      (_a6 = super.attachToResourceStore) == null ? void 0 : _a6.call(this, store);
    }
    /**
     * Override to take action when the AssignmentStore instance is replaced, either from being replaced on the project
     * or from assigning a new project.
     *
     * @param {Scheduler.data.AssignmentStore} store
     */
    attachToAssignmentStore(store) {
      var _a6;
      this.detachListeners("assignmentStore");
      (_a6 = super.attachToAssignmentStore) == null ? void 0 : _a6.call(this, store);
    }
    /**
     * Override to take action when the DependencyStore instance is replaced, either from being replaced on the project
     * or from assigning a new project.
     *
     * @param {Scheduler.data.DependencyStore} store
     */
    attachToDependencyStore(store) {
      var _a6;
      this.detachListeners("dependencyStore");
      (_a6 = super.attachToDependencyStore) == null ? void 0 : _a6.call(this, store);
    }
    /**
     * Override to take action when the CalendarManagerStore instance is replaced, either from being replaced on the
     * project or from assigning a new project.
     *
     * @param {Core.data.Store} store
     */
    attachToCalendarManagerStore(store) {
      var _a6;
      this.detachListeners("calendarManagerStore");
      (_a6 = super.attachToCalendarManagerStore) == null ? void 0 : _a6.call(this, store);
    }
    get project() {
      return this._project;
    }
    get calendarManagerStore() {
      return this.project.calendarManagerStore;
    }
    get assignmentStore() {
      return this.project.assignmentStore;
    }
    get resourceStore() {
      return this.project.resourceStore;
    }
    get eventStore() {
      return this.project.eventStore;
    }
    get dependencyStore() {
      return this.project.dependencyStore;
    }
  }, __publicField(_a5, "$name", "AttachToProjectMixin"), _a5;
};

// ../Scheduler/lib/Scheduler/view/orientation/HorizontalRendering.js
var releaseEventActions = {
  releaseElement: 1,
  // Not used at all at the moment
  reuseElement: 1
  // Used by some other element
};
var renderEventActions = {
  newElement: 1,
  reuseOwnElement: 1,
  reuseElement: 1
};
var MAX_WIDTH = 9999999;
var MIN_DATE3 = /* @__PURE__ */ new Date(0);
var MAX_DATE3 = new Date(9999, 0, 1);
var heightEventSorter = ({ startDateMS: lhs }, { startDateMS: rhs }) => lhs - rhs;
var chronoFields = {
  startDate: 1,
  endDate: 1,
  duration: 1
};
function getStartEnd(scheduler, eventRecord, useEnd, fieldName, useEventBuffer) {
  var _a5, _b2;
  const { timeAxis } = scheduler, date2 = eventRecord.isBatchUpdating && !useEventBuffer ? eventRecord.get(fieldName) : eventRecord[fieldName], hasBatchedChange = (_a5 = eventRecord.hasBatchedChange) == null ? void 0 : _a5.call(eventRecord, fieldName), useTickDates = scheduler.fillTicks && (!((_b2 = eventRecord.meta) == null ? void 0 : _b2.isResizing) || !hasBatchedChange);
  if (useTickDates) {
    let tick = timeAxis.getTickFromDate(date2);
    if (tick >= 0) {
      if (useEnd && tick === Math.round(tick) && tick > 0) {
        tick--;
      }
      const tickIndex = Math.floor(tick), tickRecord = timeAxis.getAt(tickIndex);
      return tickRecord[fieldName].getTime();
    }
  }
  return date2 == null ? void 0 : date2.getTime();
}
var HorizontalRendering = class extends Base.mixin(AttachToProjectMixin_default) {
  static get configurable() {
    return {
      /**
       * Amount of pixels to extend the current visible range at both ends with when deciding which events to
       * render. Only applies when using labels or for milestones
       * @config {Number}
       * @default
       */
      bufferSize: 150,
      verticalBufferSize: 150,
      maxZIndexStacks: 50
    };
  }
  static get properties() {
    return {
      // Map with event DOM configs, keyed by resource id
      resourceMap: /* @__PURE__ */ new Map(),
      // Map with visible events DOM configs, keyed by row instance
      rowMap: /* @__PURE__ */ new Map(),
      eventConfigs: [],
      entityNames: /* @__PURE__ */ new Set(),
      toDrawOnProjectRefresh: /* @__PURE__ */ new Set(),
      toDrawOnDataReady: /* @__PURE__ */ new Set()
    };
  }
  construct(scheduler) {
    const me = this;
    me.client = me.scheduler = scheduler;
    me.eventSorter = me.eventSorter.bind(scheduler);
    me.scrollBuffer = scheduler.scrollBuffer;
    scheduler.scrollable.ion({
      scroll: "onEarlyScroll",
      prio: 1,
      thisObj: me
    });
    scheduler.rowManager.ion({
      name: "rowManager",
      renderDone: "onRenderDone",
      removeRows: "onRemoveRows",
      translateRow: "onTranslateRow",
      offsetRows: "onOffsetRows",
      beforeRowHeight: "onBeforeRowHeightChange",
      thisObj: me
    });
    super.construct({});
  }
  init() {
  }
  updateVerticalBufferSize() {
    const { rowManager } = this.scheduler;
    if (this.scheduler.isPainted) {
      rowManager.renderRows(rowManager.rows);
    }
  }
  //endregion
  //region Region, dates & coordinates
  get visibleDateRange() {
    return this._visibleDateRange;
  }
  getDateFromXY(xy2, roundingMethod, local, allowOutOfRange = false) {
    const { scheduler } = this;
    let coord = xy2[0];
    if (!local) {
      coord = this.translateToScheduleCoordinate(coord);
    }
    coord = scheduler.getRtlX(coord);
    return scheduler.timeAxisViewModel.getDateFromPosition(coord, roundingMethod, allowOutOfRange);
  }
  translateToScheduleCoordinate(x) {
    const { scheduler } = this, { scrollable } = scheduler.timeAxisSubGrid;
    let result = x - scheduler.timeAxisSubGridElement.getBoundingClientRect().left - globalThis.scrollX;
    if (scheduler.rtl) {
      result += scrollable.maxX - Math.abs(scheduler.scrollLeft);
    } else {
      result += scheduler.scrollLeft;
    }
    return result;
  }
  translateToPageCoordinate(x) {
    const { scheduler } = this, { scrollable } = scheduler.timeAxisSubGrid;
    let result = x + scheduler.timeAxisSubGridElement.getBoundingClientRect().left;
    if (scheduler.rtl) {
      result -= scrollable.maxX - Math.abs(scheduler.scrollLeft);
    } else {
      result -= scheduler.scrollLeft;
    }
    return result;
  }
  /**
   * Gets the region, relative to the page, represented by the schedule and optionally only for a single resource.
   * This method will call getDateConstraints to allow for additional resource/event based constraints. By overriding
   * that method you can constrain events differently for different resources.
   * @param {Scheduler.model.ResourceModel} [resourceRecord] (optional) The row record
   * @param {Scheduler.model.EventModel} [eventRecord] (optional) The event record
   * @returns {Core.helper.util.Rectangle} The region of the schedule
   */
  getScheduleRegion(resourceRecord, eventRecord, local = true, dateConstraints, stretch = false) {
    var _a5;
    const me = this, { scheduler } = me, { timeAxisSubGridElement, timeAxis } = scheduler, { start, end } = (!stretch || resourceRecord) && scheduler.getResourceMarginObject(resourceRecord) || { start: 0, end: 0 };
    let region;
    if (resourceRecord) {
      const row = scheduler.getRowById(resourceRecord.id), eventElement = eventRecord && scheduler.getElementsFromEventRecord(eventRecord, resourceRecord)[0];
      region = row ? Rectangle.from(row.getElement("normal"), timeAxisSubGridElement) : scheduler.getRecordCoords(resourceRecord, true);
      if (eventElement) {
        const eventRegion = Rectangle.from(eventElement, timeAxisSubGridElement);
        region.y = eventRegion.y;
        region.bottom = eventRegion.bottom;
      } else {
        region.y = region.y + start;
        region.bottom = region.bottom - end;
      }
    } else {
      region = Rectangle.from(timeAxisSubGridElement).moveTo(null, 0);
      region.width = timeAxisSubGridElement.scrollWidth;
      region.y = region.y + start;
      region.bottom = region.bottom - end;
    }
    const taStart = timeAxis.startDate, taEnd = timeAxis.endDate;
    dateConstraints = (dateConstraints == null ? void 0 : dateConstraints.start) && dateConstraints || ((_a5 = scheduler.getDateConstraints) == null ? void 0 : _a5.call(scheduler, resourceRecord, eventRecord)) || {
      start: taStart,
      end: taEnd
    };
    dateConstraints.start = dateConstraints.start || MIN_DATE3;
    dateConstraints.end = dateConstraints.end || MAX_DATE3;
    const fullyOutSide = dateConstraints.end <= taStart || dateConstraints.start >= taEnd;
    let startX = fullyOutSide ? -1 : scheduler.getVirtualCoordFromDate(dateConstraints.start ? DateHelper.max(taStart, dateConstraints.start) : taStart), endX = fullyOutSide ? -1 : scheduler.getVirtualCoordFromDate(dateConstraints.end ? DateHelper.min(taEnd, dateConstraints.end) : taEnd);
    if (!local) {
      startX = me.translateToPageCoordinate(startX);
      endX = me.translateToPageCoordinate(endX);
    }
    region.left = Math.min(startX, endX);
    region.right = Math.max(startX, endX);
    return region;
  }
  /**
   * Gets the Region, relative to the timeline view element, representing the passed row and optionally just for a
   * certain date interval.
   * @param {Core.data.Model} rowRecord The row record
   * @param {Date} startDate A start date constraining the region
   * @param {Date} endDate An end date constraining the region
   * @returns {Core.helper.util.Rectangle} The Rectangle which encapsulates the row
   */
  getRowRegion(rowRecord, startDate, endDate) {
    const { scheduler } = this, { timeAxis } = scheduler, row = scheduler.getRowById(rowRecord.id);
    if (!row) {
      return null;
    }
    const taStart = timeAxis.startDate, taEnd = timeAxis.endDate, start = startDate ? DateHelper.max(taStart, startDate) : taStart, end = endDate ? DateHelper.min(taEnd, endDate) : taEnd, startX = scheduler.getCoordinateFromDate(start), endX = scheduler.getCoordinateFromDate(end, true, true), y = row.top, x = Math.min(startX, endX), bottom = y + row.offsetHeight;
    return new Rectangle(x, y, Math.max(startX, endX) - x, bottom - y);
  }
  getResourceEventBox(eventRecord, resourceRecord, includeOutside, roughly = false) {
    var _a5;
    const { scheduler } = this, resourceData = this.resourceMap.get(resourceRecord.id);
    let eventLayout = null, approx = false;
    if (resourceData) {
      eventLayout = resourceData.eventsData.find((d) => d.eventRecord === eventRecord);
    }
    if (!eventLayout) {
      eventLayout = this.getTimeSpanRenderData(
        eventRecord,
        resourceRecord,
        { viewport: true, timeAxis: includeOutside }
      );
      approx = true;
    }
    if (eventLayout) {
      const boxHeight = (_a5 = eventLayout.height) != null ? _a5 : scheduler.getResourceLayoutSettings(resourceRecord).contentHeight, rowBox = scheduler.rowManager.getRecordCoords(resourceRecord, true, roughly);
      if (!rowBox) {
        return null;
      }
      const absoluteTop = eventLayout.top + rowBox.top, box = new Rectangle(eventLayout.left, absoluteTop, eventLayout.width, boxHeight);
      box.layout = !approx;
      box.rowTop = rowBox.top;
      box.rowBottom = rowBox.bottom;
      box.resourceId = resourceRecord.id;
      return box;
    }
    return null;
  }
  //endregion
  //region Element <-> Record mapping
  resolveRowRecord(elementOrEvent) {
    const me = this, { scheduler } = me, element = elementOrEvent.nodeType ? elementOrEvent : elementOrEvent.target, el = element.nodeType === Element.TEXT_NODE ? element.parentElement : element, eventNode = el.closest("[data-resource-id]");
    if (eventNode) {
      return me.resourceStore.getById(eventNode.dataset.resourceId);
    }
    return scheduler.getRecordFromElement(el);
  }
  //endregion
  //region Project
  attachToProject(project) {
    super.attachToProject(project);
    this.refreshAllWhenReady = true;
    if (!this.scheduler.isConfiguring) {
      this.clearAll({ clearDom: true });
    }
    project == null ? void 0 : project.ion({
      name: "project",
      refresh: "onProjectRefresh",
      commitFinalized: "onProjectCommitFinalized",
      thisObj: this
    });
  }
  onProjectCommitFinalized() {
    const { scheduler, toDrawOnDataReady, project } = this;
    if (scheduler.isVisible) {
      if (scheduler.isPainted && !scheduler.refreshSuspended) {
        if (!toDrawOnDataReady.size && project.timeZone != null && project.ignoreRecordChanges) {
          project.resourceStore.forEach((r) => toDrawOnDataReady.add(r.id));
        }
        if (toDrawOnDataReady.size) {
          this.clearResources(toDrawOnDataReady);
          this.refreshResources(toDrawOnDataReady);
        }
        toDrawOnDataReady.clear();
      }
    } else {
      scheduler.whenVisible("refreshRows");
    }
  }
  onProjectRefresh({ isCalculated, isInitialCommit }) {
    const me = this, { scheduler, toDrawOnProjectRefresh } = me;
    if (scheduler.isVisible) {
      if (scheduler.isPainted && !scheduler.isConfiguring && !scheduler.refreshSuspended) {
        if (me.refreshAllWhenReady || isInitialCommit && isCalculated) {
          scheduler.calculateAllRowHeights(true);
          const { rowManager } = scheduler;
          if (rowManager.topRow) {
            me.clearAll();
            if (!scheduler.refreshAfterProjectRefresh) {
              if (rowManager.topRow.dataIndex >= scheduler.store.count) {
                scheduler.renderRows(false);
              } else {
                scheduler.refreshWithTransition(false, isCalculated && !isInitialCommit);
              }
            }
          } else {
            rowManager.reinitialize();
          }
          me.refreshAllWhenReady = false;
        } else if (toDrawOnProjectRefresh.size) {
          me.refreshResources(toDrawOnProjectRefresh);
        }
        toDrawOnProjectRefresh.clear();
      }
    } else {
      scheduler.whenVisible("refresh", scheduler, [true]);
    }
  }
  //endregion
  //region AssignmentStore
  attachToAssignmentStore(assignmentStore) {
    this.refreshAllWhenReady = true;
    super.attachToAssignmentStore(assignmentStore);
    if (assignmentStore) {
      assignmentStore.ion({
        name: "assignmentStore",
        changePreCommit: "onAssignmentStoreChange",
        refreshPreCommit: "onAssignmentStoreRefresh",
        thisObj: this
      });
    }
  }
  onAssignmentStoreChange({ source, action, records: assignmentRecords = [], replaced, changes }) {
    const me = this, { scheduler } = me, resourceIds = new Set(assignmentRecords.flatMap((assignmentRecord) => {
      var _a5, _b2, _c2;
      return [
        assignmentRecord.resourceId,
        // Also include any linked resources (?. twice since resource might not be resolved and point to id)
        ...(_c2 = (_b2 = (_a5 = assignmentRecord.resource) == null ? void 0 : _a5.$links) == null ? void 0 : _b2.map((link) => link.id)) != null ? _c2 : []
      ];
    }));
    if (me.resourceStore.isRemoving || me.resourceStore.isChangingId) {
      return;
    }
    switch (action) {
      case "dataset": {
        if (!me.eventStore.usesSingleAssignment) {
          if (resourceIds.size) {
            me.refreshResourcesWhenReady(resourceIds);
          } else {
            me.clearAll();
            scheduler.refreshWithTransition();
          }
        }
        return;
      }
      case "add":
      case "remove":
        me.refreshResourcesWhenReady(resourceIds);
        return;
      case "removeall":
        me.refreshAllWhenReady = true;
        return;
      case "replace":
        replaced.forEach(([oldAssignment, newAssignment]) => {
          resourceIds.add(oldAssignment.resourceId);
          resourceIds.add(newAssignment.resourceId);
        });
        me.refreshResourcesWhenReady(resourceIds);
        return;
      case "filter":
        me.clearAll();
        scheduler.calculateAllRowHeights(true);
        scheduler.refreshWithTransition();
        return;
      case "update": {
        if ("eventId" in changes || "resourceId" in changes || "id" in changes) {
          if ("resourceId" in changes) {
            resourceIds.add(changes.resourceId.oldValue);
          }
          if (source === scheduler.project.assignmentStore) {
            me.refreshResourcesOnDataReady(resourceIds);
          } else {
            me.refreshResources(resourceIds);
          }
        }
        break;
      }
      case "clearchanges": {
        const { added, modified, removed } = changes;
        if (modified.length) {
          scheduler.refreshWithTransition();
        } else {
          added.forEach((r) => resourceIds.add(r.resourceId));
          removed.forEach((r) => resourceIds.add(r.resourceId));
          me.refreshResourcesOnDataReady(resourceIds);
        }
      }
    }
  }
  onAssignmentStoreRefresh({ action, records }) {
    if (action === "batch") {
      this.clearAll();
      this.scheduler.refreshWithTransition();
    }
  }
  //endregion
  //region EventStore
  attachToEventStore(eventStore) {
    this.refreshAllWhenReady = true;
    super.attachToEventStore(eventStore);
    if (eventStore) {
      eventStore.ion({
        name: "eventStore",
        addConfirmed: "onEventStoreAddConfirmed",
        refreshPreCommit: "onEventStoreRefresh",
        thisObj: this
      });
    }
  }
  onEventStoreAddConfirmed({ record }) {
    for (const element of this.client.getElementsFromEventRecord(record)) {
      element.classList.remove("b-iscreating");
    }
  }
  onEventStoreRefresh({ action }) {
    if (action === "batch") {
      const { scheduler } = this;
      if (scheduler.isEngineReady && scheduler.isPainted) {
        this.clearAll();
        scheduler.refreshWithTransition();
      }
    }
  }
  onEventStoreChange({ action, records: eventRecords = [], record, replaced, changes, source }) {
    var _a5, _b2, _c2, _d;
    const me = this, { scheduler } = me, isResourceTimeRange = source.isResourceTimeRangeStore, resourceIds = /* @__PURE__ */ new Set();
    if (!scheduler.isPainted) {
      return;
    }
    eventRecords.forEach((eventRecord) => {
      var _a6;
      const renderedEventResources = (_a6 = eventRecord.$linkedResources) == null ? void 0 : _a6.filter((r) => me.resourceStore.includes(r));
      renderedEventResources == null ? void 0 : renderedEventResources.forEach((resourceRecord) => resourceIds.add(resourceRecord.id));
    });
    if (isResourceTimeRange) {
      switch (action) {
        case "removeall":
        case "dataset":
        case "lazyload":
          me.clearAll();
          scheduler.refreshWithTransition();
          return;
      }
      me.refreshResources(resourceIds, action !== "lazyload");
    } else {
      switch (action) {
        case "batch":
        case "sort":
        case "group":
        case "move":
          return;
        case "remove":
          return;
        case "clearchanges":
          me.clearAll();
          scheduler.refreshWithTransition();
          return;
        case "lazyload": {
          if (scheduler.isEngineReady) {
            scheduler.refreshRows();
          } else {
            me.refreshAllWhenReady = true;
          }
          return;
        }
        case "dataset": {
          me.clearAll();
          if (scheduler.isEngineReady) {
            scheduler.refreshWithTransition();
          } else {
            me.refreshAllWhenReady = true;
          }
          return;
        }
        case "add":
          break;
        case "replace":
          replaced.forEach(([, newEvent]) => {
            newEvent.resources.map((resourceRecord) => resourceIds.add(resourceRecord.id));
          });
          break;
        case "removeall":
        case "filter":
          if (!scheduler.isEngineReady) {
            me.refreshAllWhenReady = true;
            return;
          }
          me.clearAll();
          scheduler.calculateAllRowHeights(true);
          scheduler.refreshWithTransition();
          return;
        case "update": {
          const allChrono = record.$entity ? !Object.keys(changes).some((name) => !record.$entity.getField(name)) : !Object.keys(changes).some((name) => !chronoFields[name]);
          let dateChanges = 0;
          "startDate" in changes && dateChanges++;
          "endDate" in changes && dateChanges++;
          "duration" in changes && dateChanges++;
          if ("resourceId" in changes) {
            resourceIds.add(changes.resourceId.oldValue);
          }
          if (resourceIds.size && (!allChrono || // Skip case when changed "duration" only (w/o start/end affected)
          dateChanges && !("duration" in changes && dateChanges === 1) || "percentDone" in changes || "inactive" in changes || "constraintDate" in changes || "constraintType" in changes || "segments" in changes)) {
            const { eventResize, eventDragCreate } = scheduler.features, isResizing = eventResize == null ? void 0 : eventResize.isResizing, isDragCreating = eventDragCreate == null ? void 0 : eventDragCreate.isResizing, { store } = scheduler, allUsesOverlap = record.isBatchUpdating && [...resourceIds].every((resourceId) => {
              const resourceRecord = store.getById(resourceId), eventLayout = scheduler.getEventLayout(resourceRecord);
              return !scheduler.getEventLayoutHandler(eventLayout);
            });
            if (record.isBatchUpdating && (isResizing && eventResize.lockLayout || isDragCreating && eventDragCreate.lockLayout || (isResizing || isDragCreating) && allUsesOverlap)) {
              const useMinimalUpdates = isResizing && eventResize.lockLayout === "minimal-updates" || isDragCreating && eventDragCreate.lockLayout === "minimal-updates";
              for (const id2 of resourceIds) {
                const resource = scheduler.store.getById(id2);
                if (resource) {
                  if (useMinimalUpdates) {
                    const renderData = (_a5 = me.resourceMap.get(id2)) == null ? void 0 : _a5.eventsData.find((entry) => entry.eventRecord === record), row = scheduler.getRowFor(id2), domConfig = (_b2 = me.rowMap.get(row)) == null ? void 0 : _b2.find((entry) => entry.elementData.eventRecord === record);
                    if (renderData && domConfig) {
                      const newRenderData = scheduler.generateRenderData(record, store.getById(id2), true);
                      Object.assign(renderData, newRenderData);
                      const newDomConfig = this.renderEvent(renderData);
                      Object.assign(domConfig, newDomConfig);
                    }
                  } else {
                    me.refreshEventsForResource(resource, void 0, false);
                  }
                }
              }
              me.onRenderDone();
            } else if (((_c2 = me.project) == null ? void 0 : _c2.propagatingLoadChanges) || ((_d = me.project) == null ? void 0 : _d.isWritingData)) {
              me.refreshResourcesOnDataReady(resourceIds);
            } else {
              me.refreshResources(resourceIds);
            }
          }
          return;
        }
      }
      me.refreshResourcesWhenReady(resourceIds);
    }
  }
  //endregion
  //region ResourceStore
  attachToResourceStore(resourceStore) {
    this.refreshAllWhenReady = true;
    super.attachToResourceStore(resourceStore);
    if (resourceStore) {
      this.clearAll({ clearLayoutCache: true });
      resourceStore.ion({
        name: "resourceStore",
        changePreCommit: "onResourceStoreChange",
        thisObj: this
      });
    }
  }
  get resourceStore() {
    return this.client.store;
  }
  onResourceStoreChange({ action, isExpand, records, changes }) {
    const me = this, resourceIds = records == null ? void 0 : records.flatMap((r) => r.isLinked ? [r.id, r.$originalId] : [r.id]);
    if (!me.scheduler.isPainted) {
      return;
    }
    switch (action) {
      case "add":
        if (!isExpand) {
          if (records.every((r) => r.isLinked)) {
            me.refreshResources(resourceIds);
          } else {
            me.refreshResourcesWhenReady(resourceIds);
          }
        }
        return;
      case "update": {
        if (!me.project.isChangeTrackingSuspended && !changes.isLeaf) {
          me.refreshResources(resourceIds);
        }
        return;
      }
      case "filter":
        return;
      case "removeall":
        me.clearAll({ clearLayoutCache: true });
        return;
      case "dataset":
        return;
    }
    resourceIds && me.clearResources(resourceIds);
  }
  //endregion
  //region RowManager
  onTranslateRow({ row }) {
    if (row.id != null) {
      this.refreshEventsForResource(row, false);
    }
  }
  // RowManager error correction, cached layouts will no longer match.
  // Redraw to have events correctly positioned for dependency feature to draw to their elements
  onOffsetRows() {
    this.clearAll();
    this.doUpdateTimeView();
  }
  // Used to pre-calculate row heights
  calculateRowHeight(resourceRecord) {
    var _a5, _b2, _c2;
    const { scheduler } = this, rowHeight = scheduler.getResourceHeight(resourceRecord), eventLayout = scheduler.getEventLayout(resourceRecord), layoutType = eventLayout.type, resizeLock = (_a5 = scheduler.features.eventResize) == null ? void 0 : _a5.lockLayout, createLock = (_b2 = scheduler.features.eventDragCreate) == null ? void 0 : _b2.lockLayout;
    if (layoutType === "stack" && scheduler.isEngineReady && !resourceRecord.isSpecialRow && // Generated parents when TreeGrouping do not have assigned bucket
    ((_c2 = resourceRecord.assigned) == null ? void 0 : _c2.size) > 1) {
      const {
        assignmentStore,
        eventStore,
        timeAxis
      } = scheduler, {
        barMargin,
        resourceMarginObject: { total },
        contentHeight
      } = scheduler.getResourceLayoutSettings(resourceRecord), eventFilter = (eventStore.isFiltered || assignmentStore.isFiltered) && ((eventRecord) => eventRecord.assignments.some((a) => a.resource === resourceRecord.$original && assignmentStore.includes(a))), events = eventStore.getEvents({
        resourceRecord,
        includeOccurrences: scheduler.enableRecurringEvents,
        startDate: timeAxis.startDate,
        endDate: timeAxis.endDate,
        filter: eventFilter,
        fromRowHeight: true
      }).sort(heightEventSorter).map((eventRecord) => {
        var _a6, _b3;
        const useCurrent = eventRecord.isBatchUpdating && (!(resizeLock && ((_a6 = eventRecord.meta) == null ? void 0 : _a6.isResizing)) && !(createLock && ((_b3 = eventRecord.meta) == null ? void 0 : _b3.isCreating))), startDate = useCurrent ? eventRecord.get("startDate") : eventRecord.startDate, endDate = useCurrent ? eventRecord.get("endDate") : eventRecord.endDate || startDate;
        return {
          eventRecord,
          resourceRecord,
          startMS: startDate.getTime(),
          endMS: endDate.getTime()
        };
      }), layoutHandler = scheduler.getEventLayoutHandler(eventLayout), nbrOfBandsRequired = layoutHandler.layoutEventsInBands(events, resourceRecord, true);
      if (layoutHandler.type === "layoutFn") {
        return nbrOfBandsRequired;
      }
      return nbrOfBandsRequired * contentHeight + (nbrOfBandsRequired - 1) * barMargin + total;
    }
    return rowHeight;
  }
  //endregion
  //region TimeAxis
  doUpdateTimeView() {
    const { scrollable } = this.scheduler.timeAxisSubGrid;
    this.updateFromHorizontalScroll(scrollable.x, true);
  }
  onTimeAxisViewModelUpdate() {
    const me = this, { scheduler } = me;
    me.clearAll();
    if (scheduler.refreshSuspended) {
      me.detachListeners("renderingSuspend");
      scheduler.ion({
        name: "renderingSuspend",
        resumeRefresh({ trigger }) {
          if (scheduler.isEngineReady && trigger) {
            me.doUpdateTimeView();
          }
        },
        thisObj: me,
        once: true
      });
    }
    me.doUpdateTimeView();
  }
  //endregion
  //region Dependency connectors
  /**
   * Gets displaying item start side
   *
   * @param {Scheduler.model.EventModel} eventRecord
   * @returns {'start'|'end'|'top'|'bottom'} 'start' / 'end' / 'top' / 'bottom'
   */
  getConnectorStartSide(eventRecord) {
    return "start";
  }
  /**
   * Gets displaying item end side
   *
   * @param {Scheduler.model.EventModel} eventRecord
   * @returns {'start'|'end'|'top'|'bottom'} 'start' / 'end' / 'top' / 'bottom'
   */
  getConnectorEndSide(eventRecord) {
    return "end";
  }
  //endregion
  //region Scheduler hooks
  refreshRows(resourceRecords, reLayoutEvents) {
    if (resourceRecords) {
      this.clearResources(resourceRecords);
    } else if (reLayoutEvents) {
      this.clearAll();
    }
  }
  // Clear events in case they use date as part of displayed info
  onLocaleChange() {
    this.clearAll();
  }
  // Called when viewport size changes
  onViewportResize(width, height, oldWidth, oldHeight) {
    if (height > oldHeight) {
      this.onRenderDone();
    }
  }
  // Called from EventDrag
  onDragAbort({ context, dragData }) {
    const { record } = dragData;
    if (!record.isEventSegmentModel && this.resourceStore.indexOf(record.resource) < this.scheduler.topRow.dataIndex) {
      context.element.remove();
    }
  }
  // Called from EventSelection
  toggleCls(assignmentRecord, cls, add = true, useWrapper = false, checkReleased = false) {
    const element = this.client.getElementFromAssignmentRecord(assignmentRecord, useWrapper, checkReleased), resourceData = this.resourceMap.get(assignmentRecord.isModel ? assignmentRecord.get("resourceId") : assignmentRecord.resourceId), eventData = resourceData == null ? void 0 : resourceData.eventsData.find((d) => d.eventId === assignmentRecord.eventId);
    if (eventData) {
      eventData[useWrapper ? "wrapperCls" : "cls"][cls] = add;
    }
    if (element) {
      element.classList.toggle(cls, add);
      element.lastDomConfig.className[cls] = add;
    }
  }
  // React to rows being removed, refreshes view without any relayouting needed since layout is cached relative to row
  onRemoveRows({ rows }) {
    rows.forEach((row) => this.rowMap.delete(row));
    this.onRenderDone();
  }
  // Reset renderer flag before any renderers are called
  onEarlyScroll() {
    this.rendererCalled = false;
  }
  // If vertical scroll did not cause a renderer to be called we still want to update since we only draw events in
  // view, "independent" from their rows
  updateFromVerticalScroll() {
    this.fromScroll = true;
    if (!this.rendererCalled) {
      this.onRenderDone();
    }
  }
  // Update header range on horizontal scroll. No need to draw any tasks, Gantt only cares about vertical scroll
  updateFromHorizontalScroll(scrollX, force, noScrollAction) {
    var _a5, _b2;
    const me = this, {
      scheduler,
      scrollBuffer
    } = me, renderAll = scrollBuffer === -1, {
      timeAxisSubGrid,
      timeAxis
    } = scheduler, { width } = timeAxisSubGrid, { totalSize } = scheduler.timeAxisViewModel, start = scrollX, returnEnd = Math.abs(timeAxisSubGrid.scrollable.maxX) > 1 && Math.abs(timeAxisSubGrid.scrollable.maxX) <= Math.round(start) + 5, startDate = renderAll ? timeAxis.startDate : scheduler.getDateFromVirtualCoord({ coord: Math.max(0, start - scrollBuffer) }), endDate = returnEnd || renderAll ? timeAxis.endDate : scheduler.getDateFromVirtualCoord({ coord: start + width + scrollBuffer }) || timeAxis.endDate;
    if (startDate && (!scheduler._viewPresetChanging || noScrollAction) && // If rendering all, no action needed if scrolling horizontally unless start/end/tick size etc changes
    (!renderAll || force || startDate - (((_a5 = me._visibleDateRange) == null ? void 0 : _a5.startDate) || 0) || endDate - (((_b2 = me._visibleDateRange) == null ? void 0 : _b2.endDate) || 0))) {
      me._visibleDateRange = { startDate, endDate, startMS: startDate.getTime(), endMS: endDate.getTime() };
      me.viewportCoords = renderAll ? { left: 0, right: totalSize } : { left: scrollX - scrollBuffer, right: scrollX + width + scrollBuffer };
      const range = scheduler.timeView.range = { startDate, endDate };
      scheduler.internalOnVisibleDateRangeChange(range);
      if (!scheduler.refreshSuspended && scheduler.rowManager.rows.length) {
        if (scheduler.rowManager.rows[0].id === null) {
          return;
        }
        me.fromScroll = true;
        scheduler.rowManager.rows.forEach((row) => me.refreshEventsForResource(row, false, false));
        me.onRenderDone();
      }
    }
  }
  // Called from SchedulerEventRendering
  repaintEventsForResource(resourceRecord) {
    this.refreshResources([resourceRecord.id]);
  }
  onBeforeRowHeightChange() {
    this.clearAll();
  }
  //endregion
  //region Refresh resources
  refreshResourcesOnDataReady(resourceIds) {
    resourceIds.forEach((id2) => this.toDrawOnDataReady.add(id2));
  }
  /**
   * Clears resources directly and redraws them on next project refresh
   * @param {Number[]|String[]} resourceIds
   * @private
   */
  refreshResourcesWhenReady(resourceIds) {
    this.clearResources(resourceIds);
    resourceIds.forEach((id2) => this.toDrawOnProjectRefresh.add(id2));
  }
  /**
   * Clears and redraws resources directly. Respects schedulers refresh suspension
   * @param {Number[]|String[]} ids Resource ids
   * @param {Boolean} [shouldAnimate] Use transition or not
   * @private
   */
  refreshResources(ids, shouldAnimate = true) {
    const me = this, { scheduler } = me;
    me.clearResources(ids);
    if (!scheduler.refreshSuspended) {
      const rows = [], noRows = [];
      ids.forEach((id2) => {
        const row = scheduler.getRowById(id2);
        if (row) {
          rows.push(row);
        } else {
          noRows.push(row);
        }
      });
      scheduler.runWithTransition(() => {
        scheduler.calculateRowHeights(noRows.map((id2) => this.resourceStore.getById(id2)), true);
        scheduler.rowManager.renderRows(rows);
      }, shouldAnimate);
    }
  }
  //endregion
  //region Stack & pack
  layoutEventVerticallyStack(bandIndex, eventRecord, resourceRecord) {
    const {
      barMargin,
      resourceMarginObject: { start },
      contentHeight
    } = this.scheduler.getResourceLayoutSettings(resourceRecord, eventRecord.parent);
    return bandIndex === 0 ? start : start + bandIndex * contentHeight + bandIndex * barMargin;
  }
  layoutEventVerticallyPack(topFraction, heightFraction, eventRecord, resourceRecord) {
    const {
      barMargin,
      resourceMarginObject: { start },
      contentHeight
    } = this.scheduler.getResourceLayoutSettings(resourceRecord, eventRecord.parent), count = 1 / heightFraction, bandIndex = topFraction * count, height = (contentHeight - (count - 1) * barMargin) * heightFraction, top = start + bandIndex * height + bandIndex * barMargin;
    return {
      top,
      // Don't let events shrink below zero, that will cause crashes
      height: Math.max(height, 0)
    };
  }
  //endregion
  //region Render
  /**
   * Used by event drag features to bring into existence event elements that are outside of the rendered block.
   * @param {Scheduler.model.TimeSpan} eventRecord The event to render
   * @param {Scheduler.model.ResourceModel} [resourceRecord] The event to render
   * @private
   */
  addTemporaryDragElement(eventRecord, resourceRecord = eventRecord.resource) {
    const { scheduler } = this, renderData = scheduler.generateRenderData(eventRecord, resourceRecord, {
      timeAxis: true,
      viewport: true
    });
    renderData.absoluteTop = renderData.row ? renderData.top + renderData.row.top : scheduler.getResourceEventBox(eventRecord, resourceRecord, true).top;
    const domConfig = this.renderEvent(renderData), { dataset } = domConfig;
    delete domConfig.tabIndex;
    delete dataset.eventId;
    delete dataset.resourceId;
    delete dataset.assignmentId;
    delete dataset.syncId;
    dataset.transient = true;
    domConfig.parent = this.scheduler.foregroundCanvas;
    domConfig.retainElement = true;
    const result = DomHelper.createElement(domConfig, {
      reactCallback({ jsx, domConfig: domConfig2, targetElement }) {
        var _a5;
        const { reactComponent } = scheduler;
        jsx && ((_a5 = scheduler.processEventContent) == null ? void 0 : _a5.call(scheduler, {
          jsx,
          action: "add",
          domConfig: domConfig2,
          targetElement,
          isRelease: false,
          reactComponent
        }));
      }
    });
    result.innerElement = result.firstChild;
    eventRecord.instanceMeta(scheduler).hasTemporaryDragElement = true;
    return result;
  }
  // Earlier start dates are above later tasks
  // If same start date, longer tasks float to top
  // If same start + duration, sort by name
  // Fn can be called with layout date or event records (from EventNavigation)
  eventSorter(a, b) {
    if (this.overlappingEventSorter) {
      return this.overlappingEventSorter(a.eventRecord || a, b.eventRecord || b);
    }
    const startA = a.isModel ? a.startDateMS : a.dataStartMS || a.startMS, endA = a.isModel ? a.endDateMS : a.dataEndMS || a.endMS, startB = b.isModel ? b.startDateMS : b.dataStartMS || b.startMS, endB = b.isModel ? b.endDateMS : b.dataEndMS || b.endMS, nameA = a.isModel ? a.name : a.eventRecord.name, nameB = b.isModel ? b.name : b.eventRecord.name;
    return startA - startB || endB - endA || (nameA < nameB ? -1 : nameA == nameB ? 0 : 1);
  }
  /**
   * Converts a start/endDate into a MS value used when rendering the event. If scheduler is configured with
   * `fillTicks: true` the value returned will be snapped to tick start/end.
   * @private
   * @param {Scheduler.model.TimeSpan} eventRecord
   * @param {String} startDateField
   * @param {String} endDateField
   * @param {Boolean} useEventBuffer
   * @param {Scheduler.model.ResourceModel} resourceRecord
   * @returns {Object} Object of format { startMS, endMS, durationMS }
   */
  calculateMS(eventRecord, startDateField, endDateField, useEventBuffer, resourceRecord) {
    var _a5, _b2;
    const me = this, { scheduler } = me, { timeAxisViewModel } = scheduler, { eventResize } = scheduler.features;
    let startMS = getStartEnd(scheduler, eventRecord, false, startDateField, useEventBuffer), endMS = getStartEnd(scheduler, eventRecord, true, endDateField, useEventBuffer), durationMS = endMS - startMS;
    if (scheduler.milestoneLayoutMode !== "default" && durationMS === 0) {
      const pxPerMinute = timeAxisViewModel.getSingleUnitInPixels("minute"), lengthInPx = scheduler.getMilestoneLabelWidth(eventRecord, resourceRecord), duration = lengthInPx * (1 / pxPerMinute);
      durationMS = duration * 60 * 1e3;
      if (scheduler.milestoneTextPosition === "always-outside") {
        const diamondSize = scheduler.getResourceLayoutSettings(resourceRecord, eventRecord.parent).contentHeight, diamondMS = diamondSize * (1 / pxPerMinute) * 60 * 1e3;
        startMS -= diamondMS / 2;
        endMS = startMS + durationMS;
        if (scheduler.timeAxis.isContinuous === false) {
          const startX = scheduler.getCoordinateFromDate(startMS);
          endMS = (_a5 = scheduler.getDateFromCoordinate(startX + lengthInPx)) != null ? _a5 : endMS;
        }
      } else {
        switch (scheduler.milestoneAlign) {
          case "start":
          case "left":
            endMS = startMS + durationMS;
            break;
          case "end":
          case "right":
            endMS = startMS;
            startMS = endMS - durationMS;
            break;
          default:
            endMS = startMS + durationMS / 2;
            startMS = endMS - durationMS;
            break;
        }
      }
    }
    const elementStartMS = startMS, elementEndMS = endMS, elementDurationMS = durationMS;
    if (((_b2 = eventRecord.meta) == null ? void 0 : _b2.isResizing) && (eventResize == null ? void 0 : eventResize.isResizing) && eventResize.lockLayout && !eventResize.isMultiResizing) {
      startMS = eventRecord.startDate.getTime();
      endMS = eventRecord.endDate.getTime();
      durationMS = elementEndMS - elementStartMS;
    }
    return {
      startMS,
      endMS,
      durationMS,
      elementStartMS,
      elementEndMS,
      elementDurationMS
    };
  }
  /**
   * Returns event render data except actual position information.
   * @param timeSpan
   * @param rowRecord
   * @returns {HorizontalRenderData}
   * @private
   */
  setupRenderData(timeSpan, rowRecord) {
    var _a5;
    const me = this, { scheduler } = me, {
      timeAxis,
      timeAxisViewModel
    } = scheduler, {
      preamble,
      postamble
    } = timeSpan, useEventBuffer = me.isProHorizontalRendering && ((_a5 = scheduler.features.eventBuffer) == null ? void 0 : _a5.enabled) && (preamble || postamble) && !timeSpan.isMilestone, pxPerMinute = timeAxisViewModel.getSingleUnitInPixels("minute"), { isBatchUpdating } = timeSpan, startDateField = useEventBuffer ? "wrapStartDate" : "startDate", endDateField = useEventBuffer ? "wrapEndDate" : "endDate", timespanStart = isBatchUpdating && !useEventBuffer ? timeSpan.get(startDateField) : timeSpan[startDateField], timespanEnd = isBatchUpdating && !useEventBuffer ? timeSpan.get(endDateField) : timeSpan[endDateField] || timespanStart, viewStartMS = timeAxis.startMS, viewEndMS = timeAxis.endMS, msValues = me.calculateMS(timeSpan, startDateField, endDateField, useEventBuffer, rowRecord), {
      durationMS,
      elementStartMS,
      elementEndMS
    } = msValues, startsOutsideView = elementStartMS < viewStartMS | (elementStartMS > viewEndMS) << 1, endsOutsideView = elementEndMS > viewEndMS | (elementEndMS <= viewStartMS) << 1, durationMinutes = durationMS / (1e3 * 60), width = endsOutsideView ? pxPerMinute * durationMinutes : null, row = scheduler.getRowById(rowRecord);
    if (durationMS === 0) {
      msValues.startMS--;
    }
    return {
      eventRecord: timeSpan,
      taskRecord: timeSpan,
      // Helps with using Gantt projects in Scheduler Pro
      start: timespanStart,
      end: timespanEnd,
      rowId: rowRecord.id,
      children: [],
      startsOutsideView,
      endsOutsideView,
      width,
      row,
      useEventBuffer,
      ...msValues
    };
  }
  /**
   * Populates render data with information about width and horizontal position of the wrap.
   * @param {HorizontalRenderData} renderData
   * @returns {Boolean}
   * @private
   */
  fillTimeSpanHorizontalPosition(renderData) {
    const { elementStartMS: startMS, elementEndMS: endMS, elementDurationMS: durationMS } = renderData, result = startMS != null && endMS != null && this.calculateHorizontalPosition(renderData, startMS, endMS, durationMS);
    if (result) {
      Object.assign(renderData, result);
      return true;
    }
    return false;
  }
  /**
   * Fills render data with `insetInlineStart` and `width` properties
   * @param {HorizontalRenderData} renderData
   * @param {Number} startMS
   * @param {Number} endMS
   * @param {Number} durationMS
   * @returns {{insetInlineStart: number, width: number, clippedStart: boolean, clippedEnd: boolean}|null}
   * @private
   */
  calculateHorizontalPosition(renderData, startMS, endMS, durationMS) {
    const { scheduler } = this, {
      timeAxis,
      timeAxisViewModel
    } = scheduler, {
      startsOutsideView,
      endsOutsideView,
      eventRecord
    } = renderData, viewStartMS = timeAxis.startMS, pxPerMinute = timeAxisViewModel.getSingleUnitInPixels("minute");
    let width = null, endX = scheduler.getVirtualCoordFromDate(endMS, {
      local: true,
      respectExclusion: true,
      isEnd: true
    }), startX, clippedStart = false, clippedEnd = false;
    if (startsOutsideView) {
      startX = (startMS - viewStartMS) / (1e3 * 60) * pxPerMinute;
    } else {
      startX = scheduler.getVirtualCoordFromDate(startMS, {
        local: true,
        respectExclusion: true,
        isEnd: false,
        snapToNextIncluded: endX !== -1
      });
      clippedStart = startX === -1;
    }
    if (endsOutsideView) {
      const distanceToTimeAxisEnd = timeAxisViewModel.totalSize - startX, overflowDurationMS = endMS - timeAxis.endMS, overflowDurationMinutes = overflowDurationMS / (1e3 * 60);
      width = distanceToTimeAxisEnd + pxPerMinute * overflowDurationMinutes;
      if (BrowserHelper.isSafari && BrowserHelper.safariVersion < 16 && scheduler.features.stickyEvents && timeAxis.endMS && !startsOutsideView) {
        endX = scheduler.getCoordinateFromDate(timeAxis.endMS);
      } else {
        endX = startX + width;
      }
    } else {
      clippedEnd = endX === -1;
    }
    if (clippedEnd && !clippedStart) {
      endX = scheduler.getVirtualCoordFromDate(endMS, {
        local: true,
        respectExclusion: true,
        isEnd: true,
        snapToNextIncluded: true
      });
    }
    if (width > MAX_WIDTH) {
      if (startsOutsideView === 1) {
        if (endsOutsideView === 1) {
          startX = -100;
          endX = scheduler.timeAxisColumn.width + 100;
        } else {
          startX = endX - MAX_WIDTH;
        }
      } else if (endsOutsideView === 1) {
        endX = startX + MAX_WIDTH;
      }
    }
    if (clippedStart && clippedEnd) {
      startX = scheduler.getVirtualCoordFromDate(startMS, {
        local: true,
        respectExclusion: true,
        isEnd: false,
        snapToNextIncluded: true,
        max: endMS
      });
      endX = scheduler.getVirtualCoordFromDate(endMS, {
        local: true,
        respectExclusion: true,
        isEnd: true,
        snapToNextIncluded: true,
        min: startMS
      });
      if (startX === endX) {
        eventRecord.instanceMeta(scheduler).excluded = true;
        return null;
      }
    }
    return {
      left: Math.min(startX, endX),
      // Use min width 5 for normal events, 0 for milestones (won't have width specified at all in the
      // end). During drag create a normal event can get 0 duration, in this case we still want it to
      // get a min width of 5 (6px for wrapper, -1 px for event element
      width: Math.abs(endX - startX) || (eventRecord.isMilestone && !eventRecord.meta.isDragCreating ? 0 : 6),
      clippedStart,
      clippedEnd
    };
  }
  fillTimeSpanVerticalPosition(renderData, rowRecord) {
    const { scheduler } = this, { start, end } = renderData, {
      resourceMarginObject: { start: resourceMarginStart },
      contentHeight
    } = scheduler.getResourceLayoutSettings(rowRecord);
    if (scheduler.fillTicks) {
      renderData.dataStartMS = start.getTime();
      renderData.dataEndMS = end.getTime();
    }
    renderData.top = Math.max(0, resourceMarginStart);
    if (scheduler.managedEventSizing) {
      renderData.height = contentHeight;
    }
  }
  /**
   * Gets timespan coordinates etc. Relative to containing row. If the timespan is outside of the zone in
   * which timespans are rendered, that is outside of the TimeAxis, or outside of the vertical zone in which timespans
   * are rendered, then `undefined` is returned.
   * @private
   * @param {Scheduler.model.TimeSpan} timeSpan TimeSpan record
   * @param {Core.data.Model} rowRecord Row record
   * @param {Boolean|Object} includeOutside Specify true to get boxes for timespans outside of the rendered zone in both
   * dimensions. This option is used when calculating dependency lines, and we need to include routes from timespans
   * which may be outside the rendered zone.
   * @param {Boolean} includeOutside.timeAxis Pass as `true` to include timespans outside of the TimeAxis's bounds
   * @param {Boolean} includeOutside.viewport Pass as `true` to include timespans outside of the vertical timespan viewport's bounds.
   * @returns {{event/task: *, left: number, width: number, start: (Date), end: (Date), startMS: number, endMS: number, startsOutsideView: boolean, endsOutsideView: boolean}}
   */
  getTimeSpanRenderData(timeSpan, rowRecord, includeOutside = false) {
    const data = this.setupRenderData(timeSpan, rowRecord);
    if (!this.fillTimeSpanHorizontalPosition(data)) {
      return null;
    }
    this.fillTimeSpanVerticalPosition(data, rowRecord);
    return data;
  }
  // Layout a set of events, code shared by normal event render path and nested events
  layoutEvents(resourceRecord, allEvents, includeOutside = false, parentEventRecord, eventSorter) {
    const me = this, { scheduler } = me, { timeAxis } = scheduler, eventsData = allEvents.reduce((result, eventRecord) => {
      if (includeOutside || timeAxis.isTimeSpanInAxis(eventRecord)) {
        const eventBox = scheduler.generateRenderData(eventRecord, resourceRecord, includeOutside);
        if (eventBox) {
          result.push(eventBox);
        }
      }
      return result;
    }, []);
    eventsData.sort(eventSorter != null ? eventSorter : me.eventSorter);
    let rowHeight = scheduler.getAppliedResourceHeight(resourceRecord, parentEventRecord);
    const layoutEventData = eventsData.filter(({ eventRecord }) => eventRecord.isEvent && !eventRecord.meta.excludeFromLayout), eventLayout = scheduler.getEventLayout(resourceRecord, parentEventRecord), layoutHandler = scheduler.getEventLayoutHandler(eventLayout);
    if (layoutHandler) {
      const {
        barMargin,
        resourceMarginObject: { total },
        contentHeight
      } = scheduler.getResourceLayoutSettings(resourceRecord, parentEventRecord), bandsRequired = layoutHandler.applyLayout(layoutEventData, resourceRecord, scheduler) || 1;
      if (layoutHandler.type === "layoutFn") {
        rowHeight = bandsRequired;
      } else {
        rowHeight = bandsRequired * contentHeight + (bandsRequired - 1) * barMargin + total;
      }
    } else if (layoutEventData.length > 0) {
      const limit = Math.min(layoutEventData.length, me.maxZIndexStacks);
      for (let i = 0; i < limit; i++) {
        const data = layoutEventData[i];
        data.wrapperStyle += `;z-index:${i + 5}`;
      }
    }
    return { rowHeight, eventsData };
  }
  // Lay out events within a resource, relative to the resource
  layoutResourceEvents(resourceRecord, includeOutside = false) {
    const me = this, { scheduler } = me, {
      eventStore,
      assignmentStore,
      timeAxis
    } = scheduler, resourceEvents = eventStore.getEvents({
      includeOccurrences: scheduler.enableRecurringEvents,
      resourceRecord,
      startDate: timeAxis.startDate,
      endDate: timeAxis.endDate,
      visibleDateRange: me.visibleDateRange,
      filter: (assignmentStore.isFiltered || eventStore.isFiltered) && ((eventRecord) => eventRecord.assignments.some((a) => a.resource === resourceRecord.$original && assignmentStore.includes(a)))
    }), allEvents = scheduler.getEventsToRender(resourceRecord, resourceEvents) || [];
    return me.layoutEvents(resourceRecord, allEvents, includeOutside);
  }
  // Generates a DOMConfig for an EventRecord
  renderEvent(data, rowHeight) {
    const { scheduler } = this, { resourceRecord, assignmentRecord, eventRecord } = data, syncId = assignmentRecord ? this.assignmentStore.getOccurrence(assignmentRecord, eventRecord).id : data.eventId, eventElementConfig = {
      className: data.cls,
      style: data.style || "",
      children: data.children,
      role: "presentation",
      dataset: {
        // Each feature putting contents in the event wrap should have this to simplify syncing and
        // element retrieval after sync
        taskFeature: "event"
      },
      syncOptions: {
        syncIdField: "taskBarFeature"
      }
    }, elementConfig = {
      className: data.wrapperCls,
      tabIndex: "tabIndex" in data ? data.tabIndex : -1,
      "aria-label": data.wrapperCls["b-active"] ? data.ariaLabel : void 0,
      children: [
        eventElementConfig,
        ...data.wrapperChildren
      ],
      style: {
        insetBlockStart: data.absoluteTop,
        insetInlineStart: data.left,
        // ResourceTimeRanges fill row height, cannot be done earlier than this since row height is not
        // known initially
        height: data.fillSize ? rowHeight : data.height,
        // DomHelper appends px to dimensions when using numbers.
        width: data.width,
        style: data.wrapperStyle,
        fontSize: data.height + "px"
      },
      dataset: {
        // assignmentId is set in this function conditionally
        resourceId: resourceRecord.id,
        eventId: data.eventId,
        // Not using eventRecord.id to distinguish between Event and ResourceTimeRange
        syncId: resourceRecord.isLinked ? `${syncId}_${resourceRecord.id}` : syncId
      },
      // Will not be part of DOM, but attached to the element
      elementData: data,
      // Dragging etc. flags element as retained, to not reuse/release it during that operation. Events
      // always use assignments, but ResourceTimeRanges does not
      retainElement: (assignmentRecord == null ? void 0 : assignmentRecord.instanceMeta(scheduler).retainElement) || eventRecord.instanceMeta(scheduler).retainElement,
      // Options for this level of sync, lower levels can have their own
      syncOptions: {
        syncIdField: "taskFeature",
        // Remove instead of release when a feature is disabled
        releaseThreshold: 0
      }
    };
    if (eventRecord.isMilestone && scheduler.milestoneLayoutMode !== "default" && scheduler.milestoneTextPosition === "always-outside" && scheduler.getEventLayout(resourceRecord).type === "pack") {
      elementConfig.style.insetInlineStart += (scheduler.getResourceLayoutSettings(resourceRecord, eventRecord.parent).contentHeight - data.height) / 2;
    }
    if (data.fillSize) {
      data.height = rowHeight;
    }
    if (data.zIndex) {
      elementConfig.zIndex = data.zIndex;
    }
    if (assignmentRecord) {
      elementConfig.dataset.assignmentId = assignmentRecord.id;
    }
    data.elementConfig = elementConfig;
    scheduler.afterRenderEvent({ renderData: data, rowHeight, domConfig: elementConfig });
    return elementConfig;
  }
  /**
   * Refresh events for resource record (or Row), clearing its cache and forcing DOM refresh.
   * @param {Scheduler.model.ResourceModel} recordOrRow Record or row to refresh
   * @param {Boolean} [force] Specify `false` to prevent clearing cache and forcing DOM refresh
   * @internal
   */
  refreshEventsForResource(recordOrRow, force = true, draw = true) {
    const me = this, { store } = me.scheduler, record = recordOrRow.isRow ? store.getAt(recordOrRow.dataIndex) : store.getById(recordOrRow), row = me.scheduler.rowManager.getRowFor(record);
    if (force) {
      me.clearResources([record]);
    }
    if (row && record) {
      me.renderer({ row, record });
      if (force && draw) {
        me.onRenderDone();
      }
    }
  }
  // Returns layout for the current resource. Used by the renderer and exporter
  getResourceLayout(resourceRecord) {
    const me = this;
    let resourceLayout = me.resourceMap.get(resourceRecord.id);
    if (!resourceLayout || resourceLayout.invalid || me.eventStore.lazyLoad) {
      if (me.suspended) {
        return;
      }
      resourceLayout = me.layoutResourceEvents(resourceRecord, false);
      me.resourceMap.set(resourceRecord.id, resourceLayout);
    }
    return resourceLayout;
  }
  getEventDOMConfigForCurrentView(resourceLayout, row, left, right) {
    var _a5;
    const me = this, { bufferSize, scheduler } = me, { labels, eventBuffer } = scheduler.features, usesLabels = (eventBuffer == null ? void 0 : eventBuffer.enabled) || (labels == null ? void 0 : labels.enabled) && (labels.left || labels.right || labels.before || labels.after), { eventsData } = resourceLayout, reusableDOMConfigs = me.fromScroll ? me.rowMap.get(row) : null, eventDOMConfigs = [];
    let useLeft, useRight;
    for (let i = 0; i < eventsData.length; i++) {
      const layout = eventsData[i];
      useLeft = left;
      useRight = right;
      if (usesLabels || layout.width === 0) {
        useLeft -= bufferSize;
        useRight += bufferSize;
      }
      if (layout.left + layout.width >= useLeft && layout.left <= useRight) {
        layout.absoluteTop = layout.top + row.top;
        const prevDomConfig = reusableDOMConfigs == null ? void 0 : reusableDOMConfigs.find((config) => config.elementData.eventId === layout.eventId && config.elementData.resourceId === layout.resourceId);
        if (prevDomConfig) {
          prevDomConfig.style.top = layout.absoluteTop;
        }
        layout.hidden = (_a5 = row.cls) == null ? void 0 : _a5.contains("b-row-placeholder");
        eventDOMConfigs.push(prevDomConfig != null ? prevDomConfig : me.renderEvent(layout, resourceLayout.rowHeight));
      }
    }
    return eventDOMConfigs;
  }
  // Called per row in "view", collect configs
  renderer({ row, record: resourceRecord, size = {} }) {
    const me = this;
    if (resourceRecord.isSpecialRow) {
      me.rowMap.delete(row);
      return;
    }
    const { left, right } = me.viewportCoords, resourceLayout = me.getResourceLayout(resourceRecord);
    if (!resourceLayout) {
      return;
    }
    size.height = resourceLayout.rowHeight;
    size.transient = true;
    const eventDOMConfigs = me.getEventDOMConfigForCurrentView(resourceLayout, row, left, right);
    me.rowMap.set(row, eventDOMConfigs);
    me.rendererCalled = true;
  }
  // Called when the current row rendering "pass" is complete, sync collected configs to DOM
  onRenderDone() {
    var _a5;
    const { scheduler, rowMap, verticalBufferSize, fromScroll } = this;
    if (scheduler.refreshSuspended) {
      return;
    }
    const { entityNames } = this, visibleDomConfigs = { event: [] }, bodyTop = (_a5 = scheduler._scrollTop) != null ? _a5 : 0, viewTop = bodyTop - verticalBufferSize, viewBottom = bodyTop + scheduler._bodyRectangle.height + verticalBufferSize, unbuffered = verticalBufferSize < 0, unmanagedSize = !scheduler.managedEventSizing;
    for (const entityName of entityNames) {
      visibleDomConfigs[entityName] = [];
    }
    rowMap.forEach((eventDOMConfigs, row) => {
      if (!row.isDestroyed && !row.cls.contains("b-row-placeholder") && (unbuffered || row.bottom > viewTop && row.top < viewBottom)) {
        for (let i = 0; i < eventDOMConfigs.length; i++) {
          const config = eventDOMConfigs[i], data = config.elementData, {
            absoluteTop,
            eventRecord,
            entityName = "event"
          } = data;
          if (unbuffered || unmanagedSize || eventRecord.meta.isDragCreating || eventRecord.meta.isResizing || absoluteTop + data.height > viewTop && absoluteTop < viewBottom) {
            if (!visibleDomConfigs[entityName]) {
              visibleDomConfigs[entityName] = [];
              entityNames.add(entityName);
            }
            visibleDomConfigs[entityName].push(config);
          }
        }
      }
      for (let i = 0; i < eventDOMConfigs.length; i++) {
        eventDOMConfigs[i] = { ...eventDOMConfigs[i] };
      }
    });
    this.fromScroll = false;
    for (const entityName in visibleDomConfigs) {
      DomSync.sync({
        domConfig: {
          onlyChildren: true,
          children: visibleDomConfigs[entityName]
        },
        targetElement: scheduler.canvases[entityName],
        syncIdField: "syncId",
        releaseThreshold: scheduler.eventReleaseThreshold,
        // Called by DomSync when it creates, releases or reuses elements
        callback: ({ action, domConfig, lastDomConfig, targetElement, jsx }) => horizontalRenderingDomSyncCallback(scheduler, fromScroll, { action, domConfig, lastDomConfig, targetElement, jsx })
      });
    }
  }
  //endregion
  //region Cache
  // Clears cached resource layout
  clearResources(recordsOrIds) {
    recordsOrIds = ArrayHelper.asArray(recordsOrIds);
    const resourceIds = recordsOrIds.map(Model.asId);
    resourceIds.forEach((resourceId) => {
      const cached = this.resourceMap.get(resourceId);
      if (cached) {
        cached.invalid = true;
      }
      const row = this.scheduler.getRowById(resourceId);
      row && this.rowMap.delete(row);
    });
  }
  clearAll({ clearDom = false, clearLayoutCache = false } = {}) {
    const me = this, { layouts, foregroundCanvas } = me.scheduler;
    if (clearLayoutCache && layouts) {
      for (const layout in layouts) {
        layouts[layout].clearCache();
      }
    }
    if (foregroundCanvas && clearDom) {
      foregroundCanvas.syncIdMap = foregroundCanvas.lastDomConfig = foregroundCanvas.releasedIdMap = null;
      for (const child of foregroundCanvas.children) {
        child.lastDomConfig = child.elementData = null;
      }
    }
    me.resourceMap.clear();
    me.rowMap.clear();
  }
  //endregion
};
//region Config & Init
__publicField(HorizontalRendering, "$name", "HorizontalRendering");
function horizontalRenderingDomSyncCallback(scheduler, fromScroll, { action, domConfig, lastDomConfig, targetElement, jsx }) {
  var _a5, _b2, _c2;
  const { reactComponent } = scheduler, isRelease = releaseEventActions[action], isRender = renderEventActions[action];
  !isRelease && ((_a5 = scheduler.processEventContent) == null ? void 0 : _a5.call(scheduler, {
    jsx,
    action,
    domConfig,
    targetElement,
    isRelease,
    reactComponent,
    scrolling: fromScroll
  }));
  if (action === "none" || !((_b2 = domConfig == null ? void 0 : domConfig.elementData) == null ? void 0 : _b2.isWrap)) {
    return;
  }
  if (isRelease && (lastDomConfig == null ? void 0 : lastDomConfig.elementData)) {
    const { eventRecord, resourceRecord, assignmentRecord } = lastDomConfig.elementData, event = {
      renderData: lastDomConfig.elementData,
      element: targetElement,
      eventRecord,
      resourceRecord,
      assignmentRecord
    };
    (_c2 = scheduler.processEventContent) == null ? void 0 : _c2.call(scheduler, {
      isRelease,
      domConfig: lastDomConfig,
      targetElement,
      reactComponent
    });
    if (targetElement === DomHelper.getActiveElement(targetElement)) {
      scheduler.focusElement.focus();
    }
    scheduler.trigger("releaseEvent", event);
  }
  if (isRender) {
    const { eventRecord, resourceRecord, assignmentRecord } = domConfig.elementData, event = {
      renderData: domConfig.elementData,
      element: targetElement,
      isReusingElement: action === "reuseElement",
      isRepaint: action === "reuseOwnElement",
      eventRecord,
      resourceRecord,
      assignmentRecord
    };
    if (action === "reuseElement" && scheduler.isAnimating) {
      DomHelper.addTemporaryClass(targetElement, "b-reusing-own", 50, scheduler, `b-reusing-own-${(assignmentRecord || eventRecord).id}`);
    }
    scheduler.trigger("renderEvent", event);
  }
}
HorizontalRendering._$name = "HorizontalRendering";

// ../Scheduler/lib/Scheduler/eventlayout/VerticalLayout.js
var VerticalLayout = class extends PackMixin_default() {
  static get defaultConfig() {
    return {
      coordProp: "leftFactor",
      sizeProp: "widthFactor"
    };
  }
  // Try to pack the events to consume as little space as possible
  applyLayout(events, resourceRecord, parentEventRecord) {
    const me = this, columnWidth = me.scheduler.getResourceWidth(resourceRecord, parentEventRecord), {
      barMargin,
      resourceMarginObject: { start: resourceMarginStart, total: resourceMarginTotal }
    } = me.scheduler.getResourceLayoutSettings(resourceRecord, parentEventRecord), eventLayout = me.scheduler.getEventLayout(resourceRecord, parentEventRecord), layoutType = eventLayout.type;
    return me.packEventsInBands(events, (tplData, clusterIndex, slot, slotSize) => {
      if (layoutType === "none") {
        tplData.width = columnWidth - resourceMarginTotal;
        tplData.left += resourceMarginStart;
      } else {
        tplData.widthFactor = slotSize;
        const leftFactor = tplData.leftFactor = slot.start + clusterIndex * slotSize, packColumnCount = Math.round(1 / slotSize), packColumnIndex = leftFactor / slotSize, availableWidth = columnWidth - resourceMarginTotal - barMargin * (packColumnCount - 1);
        if (layoutType === "mixed" && packColumnCount === 2) {
          tplData.left += leftFactor * columnWidth / 5 + barMargin;
          tplData.width = columnWidth - leftFactor * columnWidth / 5 - barMargin * 2;
          tplData.zIndex = 5 + packColumnIndex;
        } else {
          tplData.width = slotSize * availableWidth;
          tplData.appliedMargin = resourceMarginStart + barMargin * packColumnIndex;
          tplData.left += leftFactor * availableWidth + tplData.appliedMargin;
        }
      }
      tplData.cls["b-sch-event-narrow"] = tplData.width < me.scheduler.narrowEventWidth;
    });
  }
};
VerticalLayout._$name = "VerticalLayout";

// ../Scheduler/lib/Scheduler/view/orientation/VerticalRendering.js
var releaseEventActions2 = {
  releaseElement: 1,
  // Not used at all at the moment
  reuseElement: 1
  // Used by some other element
};
var renderEventActions2 = {
  newElement: 1,
  reuseOwnElement: 1,
  reuseElement: 1
};
var chronoFields2 = {
  startDate: 1,
  endDate: 1,
  duration: 1
};
var emptyObject13 = Object.freeze({});
var VerticalRendering = class extends Base.mixin(Delayable_default, AttachToProjectMixin_default) {
  //region Config & Init
  static get properties() {
    return {
      eventMap: /* @__PURE__ */ new Map(),
      resourceMap: /* @__PURE__ */ new Map(),
      releasedElements: {},
      toDrawOnProjectRefresh: /* @__PURE__ */ new Set(),
      resourceBufferSize: 1
    };
  }
  construct(scheduler) {
    this.client = this.scheduler = scheduler;
    this.verticalLayout = new VerticalLayout({ scheduler });
    super.construct({});
  }
  init() {
    const me = this, { scheduler, resourceColumns } = me;
    resourceColumns.resourceStore = me.resourceStore;
    resourceColumns.ion({
      name: "resourceColumns",
      columnWidthChange: "onResourceColumnWidthChange",
      thisObj: me
    });
    me.initialized = true;
    if (scheduler.isPainted) {
      me.renderer();
    }
    resourceColumns.availableWidth = scheduler.timeAxisSubGridElement.offsetWidth;
  }
  //endregion
  //region Elements <-> Records
  resolveRowRecord(elementOrEvent, xy2) {
    const me = this, { scheduler } = me, event = elementOrEvent.nodeType ? null : elementOrEvent, element = event ? event.target : elementOrEvent, coords = event ? [event.borderOffsetX, event.borderOffsetY] : xy2, el = element.nodeType === Element.TEXT_NODE ? element.parentElement : element, eventElement = el.closest(scheduler.eventSelector);
    if (eventElement) {
      return scheduler.resourceStore.getById(eventElement.dataset.resourceId);
    }
    if (!element.closest(".b-sch-timeaxis-cell")) {
      return null;
    }
    if (!coords) {
      throw new Error(`Vertical mode needs coordinates to resolve this element. Can also be called with a browser
                event instead of element to extract element and coordinates from`);
    }
    if (scheduler.variableColumnWidths || scheduler.resourceStore.isGrouped) {
      let totalWidth = 0;
      for (const col of me.resourceStore) {
        if (!col.isSpecialRow) {
          totalWidth += scheduler.getResourceWidth(col);
        }
        if (totalWidth >= coords[0]) {
          return col;
        }
      }
      return null;
    }
    const index = Math.floor(coords[0] / me.resourceColumns.columnWidth);
    return me.allResourceRecords[index];
  }
  toggleCls(assignmentRecord, cls, add = true, useWrapper = false) {
    var _a5;
    const eventData = (_a5 = this.eventMap.get(assignmentRecord.eventId)) == null ? void 0 : _a5[assignmentRecord.resourceId];
    if (eventData) {
      eventData.renderData[useWrapper ? "wrapperCls" : "cls"][cls] = add;
      const element = this.client.getElementFromAssignmentRecord(assignmentRecord, useWrapper);
      if (element) {
        element.classList[add ? "add" : "remove"](cls);
      }
    }
  }
  //endregion
  //region Coordinate <-> Date
  getDateFromXY(xy2, roundingMethod, local, allowOutOfRange = false) {
    let coord = xy2[1];
    if (!local) {
      coord = this.translateToScheduleCoordinate(coord);
    }
    return this.scheduler.timeAxisViewModel.getDateFromPosition(coord, roundingMethod, allowOutOfRange);
  }
  translateToScheduleCoordinate(y) {
    return y - this.scheduler.timeAxisSubGridElement.getBoundingClientRect().top - globalThis.scrollY;
  }
  translateToPageCoordinate(y) {
    return y + this.scheduler.timeAxisSubGridElement.getBoundingClientRect().top + globalThis.scrollY;
  }
  //endregion
  //region Regions
  getResourceEventBox(event, resource) {
    var _a5, _b2, _c2;
    const eventId = event.id, resourceId = resource.id;
    let { renderData } = ((_a5 = this.eventMap.get(eventId)) == null ? void 0 : _a5[resourceId]) || emptyObject13;
    if (!renderData) {
      this.layoutResourceEvents(this.scheduler.resourceStore.getById(resourceId));
      renderData = (_c2 = (_b2 = this.eventMap.get(eventId)) == null ? void 0 : _b2[resourceId]) == null ? void 0 : _c2.renderData;
    }
    return renderData ? new Rectangle(renderData.left, renderData.top, renderData.width, renderData.bottom - renderData.top) : null;
  }
  getScheduleRegion(resourceRecord, eventRecord, local) {
    var _a5;
    const me = this, { scheduler } = me, region = Rectangle.from(scheduler.timeAxisSubGridElement, scheduler.timeAxisSubGridElement);
    if (resourceRecord) {
      region.left = me.allResourceRecords.indexOf(resourceRecord) * scheduler.resourceColumnWidth;
      region.right = region.left + scheduler.resourceColumnWidth;
    }
    const start = scheduler.timeAxis.startDate, end = scheduler.timeAxis.endDate, dateConstraints = ((_a5 = scheduler.getDateConstraints) == null ? void 0 : _a5.call(scheduler, resourceRecord, eventRecord)) || {
      start,
      end
    }, startY = scheduler.getCoordinateFromDate(DateHelper.max(start, dateConstraints.start)), endY = scheduler.getCoordinateFromDate(DateHelper.min(end, dateConstraints.end));
    if (!local) {
      region.top = me.translateToPageCoordinate(startY);
      region.bottom = me.translateToPageCoordinate(endY);
    } else {
      region.top = startY;
      region.bottom = endY;
    }
    return region;
  }
  getRowRegion(resourceRecord, startDate, endDate) {
    const me = this, { scheduler } = me, x = me.allResourceRecords.indexOf(resourceRecord) * scheduler.resourceColumnWidth, taStart = scheduler.timeAxis.startDate, taEnd = scheduler.timeAxis.endDate, start = startDate ? DateHelper.max(taStart, startDate) : taStart, end = endDate ? DateHelper.min(taEnd, endDate) : taEnd, startY = scheduler.getCoordinateFromDate(start), endY = scheduler.getCoordinateFromDate(end, true, true), y = Math.min(startY, endY), height = Math.abs(startY - endY);
    return new Rectangle(x, y, scheduler.resourceColumnWidth, height);
  }
  get visibleDateRange() {
    const scheduler = this.scheduler, scrollPos = scheduler.scrollable.y, height = scheduler.scrollable.clientHeight, startDate = scheduler.getDateFromCoordinate(scrollPos) || scheduler.timeAxis.startDate, endDate = scheduler.getDateFromCoordinate(scrollPos + height) || scheduler.timeAxis.endDate;
    return {
      startDate,
      endDate,
      startMS: startDate.getTime(),
      endMS: endDate.getTime()
    };
  }
  //endregion
  //region Events
  // Column width changed, rerender fully
  onResourceColumnWidthChange({ width, oldWidth }) {
    const me = this, { scheduler } = me;
    me.resourceColumns.width = scheduler.timeAxisColumn.width = me.allResourceRecords.length * width;
    me.clearAll();
    me.refresh(Math.abs(width - oldWidth) > 30);
    scheduler.onVisibleResourceColumnChange(width, oldWidth);
  }
  //endregion
  //region Project
  attachToProject(project) {
    super.attachToProject(project);
    if (project) {
      project.ion({
        name: "project",
        refresh: "onProjectRefresh",
        thisObj: this
      });
    }
  }
  onProjectRefresh() {
    const me = this, { scheduler, toDrawOnProjectRefresh } = me;
    if (scheduler.isVisible) {
      if (scheduler.rendered && !scheduler.refreshSuspended) {
        if (me.refreshAllWhenReady) {
          me.clearAll();
          me.refresh();
          me.refreshAllWhenReady = false;
        } else if (toDrawOnProjectRefresh.size) {
          me.refresh();
        }
        toDrawOnProjectRefresh.clear();
      }
    } else {
      scheduler.whenVisible("refresh", scheduler, [true]);
    }
  }
  //endregion
  //region EventStore
  attachToEventStore(eventStore) {
    super.attachToEventStore(eventStore);
    this.refreshAllWhenReady = true;
    if (eventStore) {
      eventStore.ion({
        name: "eventStore",
        addConfirmed: "onEventStoreAddConfirmed",
        refreshPreCommit: "onEventStoreRefresh",
        thisObj: this
      });
    }
  }
  onEventStoreAddConfirmed({ record }) {
    for (const element of this.client.getElementsFromEventRecord(record)) {
      element.classList.remove("b-iscreating");
    }
  }
  onEventStoreRefresh({ action }) {
    if (action === "batch") {
      this.refreshAllWhenReady = true;
    }
  }
  onEventStoreChange({ action, records: eventRecords = [], record, replaced, changes, isAssign }) {
    const me = this, resourceIds = /* @__PURE__ */ new Set();
    eventRecords.forEach((eventRecord) => {
      var _a5;
      const renderedEventResources = (_a5 = eventRecord.$linkedResources) == null ? void 0 : _a5.filter((r) => me.resourceStore.includes(r));
      renderedEventResources == null ? void 0 : renderedEventResources.forEach((resourceRecord) => resourceIds.add(resourceRecord.id));
    });
    switch (action) {
      case "sort":
      case "group":
      case "move":
      case "remove":
      case "batch":
        return;
      case "dataset":
        me.refreshAllResourcesWhenReady();
        return;
      case "add":
        break;
      case "replace":
        replaced.forEach(([, newEvent]) => {
          newEvent.resources.map((resourceRecord) => resourceIds.add(resourceRecord.id));
        });
        me.clearResources(resourceIds);
        break;
      case "removeall":
      case "filter":
        me.clearAll();
        me.refresh();
        return;
      case "update": {
        const allChrono = record.$entity ? !Object.keys(changes).some((name) => !record.$entity.getField(name)) : !Object.keys(changes).some((name) => !chronoFields2[name]);
        let changeCount = 0;
        if ("startDate" in changes)
          changeCount++;
        if ("endDate" in changes)
          changeCount++;
        if ("duration" in changes)
          changeCount++;
        if (!allChrono || changeCount || "percentDone" in changes || "inactive" in changes || "segments" in changes) {
          if (me.shouldWaitForInitializeAndEngineReady) {
            me.refreshResourcesWhenReady(resourceIds);
          } else {
            me.clearResources(resourceIds);
            me.refresh();
          }
        }
        return;
      }
    }
    me.refreshResourcesWhenReady(resourceIds);
  }
  //endregion
  //region ResourceStore
  attachToResourceStore(resourceStore) {
    const me = this;
    super.attachToResourceStore(resourceStore);
    me.refreshAllWhenReady = true;
    if (me.resourceColumns) {
      me.resourceColumns.resourceStore = resourceStore;
    }
    resourceStore.ion({
      name: "resourceStore",
      changePreCommit: "onResourceStoreChange",
      refreshPreCommit: "onResourceStoreRefresh",
      // In vertical, resource store is not the row store but should toggle the load mask
      load: () => me.scheduler.unmaskBody(),
      thisObj: me,
      prio: 1
      // Call before others to clear cache before redraw
    });
    if (me.initialized && me.scheduler.isPainted) {
      me.firstResource = me.lastResource = null;
      me.clearAll();
      me.renderer();
    }
  }
  onResourceStoreChange({ source: resourceStore, action, records = [], record, replaced, changes }) {
    const me = this, resourceRecords = replaced ? replaced.map((r) => r[1]) : records, resourceIds = new Set(resourceRecords.map((resourceRecord) => resourceRecord.id));
    me.firstResource = me.lastResource = null;
    resourceStore._allResourceRecords = null;
    const { allResourceRecords } = resourceStore;
    if (me.scheduler.isEngineReady) {
      switch (action) {
        case "update":
          if (changes == null ? void 0 : changes.id) {
            me.clearResources([changes.id.oldValue, changes.id.value]);
          } else {
            me.clearResources([record.id]);
          }
          break;
        case "filter":
          me.clearAll();
          break;
      }
      if (changes && "columnWidth" in changes) {
        record.instanceMeta(me.scheduler).columnWidth = record.columnWidth;
        me.clearAll();
      }
      me.refresh(true);
    } else {
      switch (action) {
        case "dataset":
        case "remove":
        case "removeall":
          me.refreshAllResourcesWhenReady();
          return;
        case "replace":
        case "add": {
          if (!resourceStore.isGrouped) {
            const firstIndex = resourceRecords.reduce(
              (index, record2) => Math.min(index, allResourceRecords.indexOf(record2)),
              allResourceRecords.length
            );
            for (let i = firstIndex; i < allResourceRecords.length; i++) {
              resourceIds.add(allResourceRecords[i].id);
            }
          }
        }
      }
      me.refreshResourcesWhenReady(resourceIds);
    }
  }
  onResourceStoreRefresh({ action }) {
    const me = this;
    if (action === "sort" || action === "group") {
      me.firstResource = me.lastResource = me.resourceStore._allResourceRecords = null;
      me.clearAll();
      me.refresh();
    }
  }
  //endregion
  //region AssignmentStore
  attachToAssignmentStore(assignmentStore) {
    super.attachToAssignmentStore(assignmentStore);
    this.refreshAllWhenReady = true;
    if (assignmentStore) {
      assignmentStore.ion({
        name: "assignmentStore",
        changePreCommit: "onAssignmentStoreChange",
        refreshPreCommit: "onAssignmentStoreRefresh",
        thisObj: this
      });
    }
  }
  onAssignmentStoreChange({ action, records: assignmentRecords = [], replaced, changes }) {
    const me = this, resourceIds = new Set(assignmentRecords.map((assignmentRecord) => assignmentRecord.resourceId));
    if (me.scheduler.isEngineReady) {
      switch (action) {
        case "add":
        case "remove":
          me.clearResources(resourceIds);
          break;
        case "filter":
          me.clearAll();
          break;
        case "update": {
          if ("resourceId" in changes) {
            resourceIds.add(changes.resourceId.oldValue);
          }
          if (!Object.keys(changes).filter((field) => field !== "resource" && field !== "event").length) {
            return;
          }
          me.clearResources(resourceIds);
        }
      }
      me.refresh(true);
    } else {
      if (changes && "resourceId" in changes) {
        resourceIds.add(changes.resourceId.oldValue);
      }
      switch (action) {
        case "removeall":
          me.refreshAllResourcesWhenReady();
          return;
        case "replace":
          replaced.forEach(([oldAssignment, newAssignment]) => {
            resourceIds.add(oldAssignment.resourceId);
            resourceIds.add(newAssignment.resourceId);
          });
      }
      me.refreshResourcesWhenReady(resourceIds);
    }
  }
  onAssignmentStoreRefresh({ action, records }) {
    if (action === "batch") {
      this.clearAll();
      this.refreshAllResourcesWhenReady();
    }
  }
  //endregion
  //region View hooks
  refreshRows(resourceRecords, reLayoutEvents) {
    if (reLayoutEvents) {
      this.clearAll();
      this.scheduler.refreshFromRerender = false;
    }
  }
  // Called from SchedulerEventRendering
  repaintEventsForResource(resourceRecord) {
    const { resourceColumns } = this.scheduler;
    this.renderResource(resourceRecord);
    if (this.scheduler.internalEventLayout.type === "layoutFn") {
      resourceColumns.updateWidthCache();
      resourceColumns.refresh();
    }
  }
  updateFromHorizontalScroll(scrollX) {
    if (scrollX !== this.prevScrollX) {
      this.fromScroll = true;
      this.renderer();
      this.prevScrollX = scrollX;
    }
  }
  updateFromVerticalScroll() {
    this.fromScroll = true;
    this.renderer();
  }
  scrollResourceIntoView(resourceRecord, options) {
    const { scheduler } = this, x = this.allResourceRecords.indexOf(resourceRecord) * scheduler.resourceColumnWidth;
    return scheduler.scrollHorizontallyTo(x, options);
  }
  get allResourceRecords() {
    return this.scheduler.resourceStore.allResourceRecords;
  }
  // Called when viewport size changes
  onViewportResize(width) {
    if (this.resourceColumns.availableWidth !== width) {
      this.resourceColumns.availableWidth = width;
      this.scheduler.refreshVirtualScrollbars();
    }
    this.renderer();
  }
  get resourceColumns() {
    var _a5;
    return (_a5 = this.scheduler.timeAxisColumn) == null ? void 0 : _a5.resourceColumns;
  }
  // Clear events in case they use date as part of displayed info
  onLocaleChange() {
    this.clearAll();
  }
  // No need to do anything special
  onDragAbort() {
  }
  onBeforeRowHeightChange() {
  }
  onTimeAxisViewModelUpdate() {
  }
  updateElementId() {
  }
  releaseTimeSpanDiv() {
  }
  //endregion
  //region Dependency connectors
  /**
   * Gets displaying item start side
   *
   * @param {Scheduler.model.EventModel} eventRecord
   * @returns {'top'|'left'|'bottom'|'right'} 'left' / 'right' / 'top' / 'bottom'
   */
  getConnectorStartSide(eventRecord) {
    return "top";
  }
  /**
   * Gets displaying item end side
   *
   * @param {Scheduler.model.EventModel} eventRecord
   * @returns {'top'|'left'|'bottom'|'right'} 'left' / 'right' / 'top' / 'bottom'
   */
  getConnectorEndSide(eventRecord) {
    return "bottom";
  }
  //endregion
  //region Refresh resources
  /**
   * Clears resources directly and redraws them on next project refresh
   * @param {Number[]|String[]} resourceIds
   * @private
   */
  refreshResourcesWhenReady(resourceIds) {
    this.clearResources(resourceIds);
    resourceIds.forEach((id2) => this.toDrawOnProjectRefresh.add(id2));
  }
  /**
   * Clears all resources directly and redraws them on next project refresh
   * @private
   */
  refreshAllResourcesWhenReady() {
    this.clearAll();
    this.refreshAllWhenReady = true;
  }
  //region Rendering
  // Resources in view + buffer
  get resourceRange() {
    return this.getResourceRange(true);
  }
  // Resources strictly in view
  get visibleResources() {
    const { first, last } = this.getResourceRange();
    return {
      first: this.allResourceRecords[first],
      last: this.allResourceRecords[last]
    };
  }
  getResourceRange(withBuffer) {
    const {
      scheduler,
      resourceStore
    } = this, {
      resourceColumnWidth,
      scrollX
    } = scheduler, {
      scrollWidth
    } = scheduler.timeAxisSubGrid.scrollable, resourceBufferSize = withBuffer ? this.resourceBufferSize : 0, viewportStart = scrollX - resourceBufferSize, viewportEnd = scrollX + scrollWidth + resourceBufferSize;
    if (!(resourceStore == null ? void 0 : resourceStore.count)) {
      return { first: -1, last: -1 };
    }
    if (scheduler.variableColumnWidths) {
      let first, last = 0, start, end = 0;
      this.allResourceRecords.forEach((resource, i) => {
        resource.instanceMeta(scheduler).insetStart = start = end;
        end = start + scheduler.getResourceWidth(resource);
        if (start > viewportEnd) {
          return false;
        }
        if (end > viewportStart && first == null) {
          first = i;
        } else if (start < viewportEnd) {
          last = i;
        }
      });
      return { first, last };
    } else {
      return {
        first: Math.max(Math.floor(scrollX / resourceColumnWidth) - resourceBufferSize, 0),
        last: Math.min(
          Math.floor((scrollX + scheduler.timeAxisSubGrid.width) / resourceColumnWidth) + resourceBufferSize,
          this.allResourceRecords.length - 1
        )
      };
    }
  }
  // Dates in view + buffer
  get dateRange() {
    const { scheduler } = this;
    let bottomDate = scheduler.getDateFromCoordinate(
      Math.min(
        scheduler.scrollTop + scheduler.bodyHeight + scheduler.tickSize - 1,
        (scheduler.virtualScrollHeight || scheduler.scrollable.scrollHeight) - 1
      )
    );
    if (!bottomDate) {
      bottomDate = scheduler.timeAxis.last.endDate;
    }
    let topDate = scheduler.getDateFromCoordinate(Math.max(scheduler.scrollTop - scheduler.tickSize, 0));
    if (!topDate) {
      topDate = scheduler.timeAxis.first.startDate;
      bottomDate = scheduler.getDateFromCoordinate(scheduler.bodyHeight + scheduler.tickSize - 1);
    }
    return {
      topDate,
      bottomDate
    };
  }
  getTimeSpanRenderData(eventRecord, resourceRecord, includeOutside = false) {
    var _a5;
    const me = this, {
      scheduler
    } = me, {
      preamble,
      postamble
    } = eventRecord, {
      variableColumnWidths
    } = scheduler, useEventBuffer = ((_a5 = scheduler.features.eventBuffer) == null ? void 0 : _a5.enabled) && me.isProVerticalRendering && (preamble || postamble) && !eventRecord.isMilestone, startDateField = useEventBuffer ? "wrapStartDate" : "startDate", endDateField = useEventBuffer ? "wrapEndDate" : "endDate", startDate = eventRecord.isBatchUpdating && eventRecord.hasBatchedChange(startDateField) && !useEventBuffer ? eventRecord.get(startDateField) : eventRecord[startDateField], endDate = eventRecord.isBatchUpdating && eventRecord.hasBatchedChange(endDateField) && !useEventBuffer ? eventRecord.get(endDateField) : eventRecord[endDateField], {
      resourceMarginObject: { total }
    } = scheduler.getResourceLayoutSettings(resourceRecord), top = scheduler.getCoordinateFromDate(startDate), instanceMeta = resourceRecord.instanceMeta(scheduler), left = variableColumnWidths ? instanceMeta.insetStart : me.allResourceRecords.indexOf(resourceRecord) * scheduler.resourceColumnWidth, resourceWidth = scheduler.getResourceWidth(resourceRecord), width = resourceWidth - total, startDateMS = startDate.getTime(), endDateMS = endDate.getTime();
    let bottom = scheduler.getCoordinateFromDate(endDate), height = bottom - top;
    if (bottom === -1) {
      height = Math.round((endDateMS - startDateMS) * scheduler.timeAxisViewModel.getSingleUnitInPixels("millisecond"));
      bottom = top + height;
    }
    return {
      eventRecord,
      resourceRecord,
      left,
      top,
      bottom,
      resourceWidth,
      width,
      height,
      startDate,
      endDate,
      startDateMS,
      endDateMS,
      useEventBuffer,
      children: [],
      start: startDate,
      end: endDate,
      startMS: startDateMS,
      endMS: endDateMS
    };
  }
  // Earlier start dates are above later tasks
  // If same start date, longer tasks float to top
  // If same start + duration, sort by name
  eventSorter(a, b) {
    const startA = a.dataStartMs || a.startDateMS, endA = a.dataEndMs || a.endDateMS, startB = b.dataStartMs || b.startDateMS, endB = b.dataEndMs || b.endDateMS, nameA = a.isModel ? a.name : a.eventRecord.name, nameB = b.isModel ? b.name : b.eventRecord.name;
    return startA - startB || endB - endA || (nameA < nameB ? -1 : nameA == nameB ? 0 : 1);
  }
  layoutEvents(resourceRecord, allEvents, includeOutside = false, parentEventRecord, eventSorter) {
    const me = this, { scheduler } = me, {
      variableColumnWidths
    } = scheduler, { id: resourceId } = resourceRecord, instanceMeta = resourceRecord.instanceMeta(scheduler), cacheKey2 = parentEventRecord ? `${resourceId}-${parentEventRecord.id}` : resourceId, cache = me.resourceMap.set(cacheKey2, {}).get(cacheKey2), resourceIndex = me.allResourceRecords.indexOf(resourceRecord);
    const layoutData = allEvents.reduce((toLayout, eventRecord) => {
      if (eventRecord.isScheduled) {
        const renderData = scheduler.generateRenderData(eventRecord, resourceRecord, false), eventData = { renderData }, eventResources = ObjectHelper.getMapPath(me.eventMap, renderData.eventId, {});
        eventResources[resourceId] = eventData;
        cache[renderData.eventId] = eventData;
        if (renderData.fillSize) {
          renderData.left = variableColumnWidths ? instanceMeta.insetStart : resourceIndex * scheduler.resourceColumnWidth;
          renderData.width = scheduler.getResourceWidth(resourceRecord);
        } else {
          toLayout.push(renderData);
        }
      }
      return toLayout;
    }, []);
    layoutData.sort(eventSorter != null ? eventSorter : me.eventSorter);
    me.verticalLayout.applyLayout(
      layoutData,
      resourceRecord,
      parentEventRecord
    );
    return cache;
  }
  // Calculate the layout for all events assigned to a resource. Since we are never stacking, the layout of one
  // resource will never affect the others
  layoutResourceEvents(resourceRecord, startDate, endDate) {
    const me = this, { scheduler } = me, {
      assignmentStore,
      eventStore,
      timeAxis
    } = scheduler;
    let events = eventStore.getEvents({
      includeOccurrences: scheduler.enableRecurringEvents,
      resourceRecord,
      startDate: startDate != null ? startDate : timeAxis.startDate,
      endDate: endDate != null ? endDate : timeAxis.endDate,
      filter: (assignmentStore.isFiltered || eventStore.isFiltered) && ((eventRecord) => eventRecord.assignments.some((a) => a.resource === resourceRecord && assignmentStore.includes(a)))
    });
    events = scheduler.getEventsToRender(resourceRecord, events);
    return me.layoutEvents(resourceRecord, events);
  }
  /**
   * Used by event drag features to bring into existence event elements that are outside of the rendered block.
   * @param {Scheduler.model.TimeSpan} eventRecord The event to render
   * @private
   */
  addTemporaryDragElement(eventRecord) {
    const { scheduler } = this, renderData = scheduler.generateRenderData(
      eventRecord,
      eventRecord.resource,
      { timeAxis: true, viewport: true }
    );
    renderData.top = renderData.row ? renderData.top + renderData.row.top : scheduler.getResourceEventBox(eventRecord, eventRecord.resource, true).top;
    const domConfig = this.renderEvent({ renderData }), { dataset } = domConfig;
    delete domConfig.tabIndex;
    delete dataset.eventId;
    delete dataset.resourceId;
    delete dataset.assignmentId;
    delete dataset.syncId;
    dataset.transient = true;
    domConfig.parent = this.scheduler.foregroundCanvas;
    domConfig.retainElement = true;
    const result = DomHelper.createElement(domConfig, {
      reactCallback({ jsx, domConfig: domConfig2, targetElement }) {
        var _a5;
        const { reactComponent } = scheduler;
        jsx && ((_a5 = scheduler.processEventContent) == null ? void 0 : _a5.call(scheduler, {
          jsx,
          action: "add",
          domConfig: domConfig2,
          targetElement,
          isRelease: false,
          reactComponent
        }));
      }
    });
    result.innerElement = result.firstChild;
    eventRecord.instanceMeta(scheduler).hasTemporaryDragElement = true;
    return result;
  }
  // To update an event, first release its element and then render it again.
  // The element will be reused and updated. Keeps code simpler
  renderEvent(eventData) {
    const { scheduler } = this, data = eventData.renderData, {
      resourceRecord,
      assignmentRecord,
      eventRecord
    } = data, elementConfig = {
      className: data.wrapperCls,
      tabIndex: -1,
      "aria-label": data.wrapperCls["b-active"] ? data.ariaLabel : void 0,
      children: [
        {
          role: "presentation",
          className: data.cls,
          style: (data.internalStyle || "") + (data.style || ""),
          children: data.children,
          dataset: {
            // Each feature putting contents in the event wrap should have this to simplify syncing and
            // element retrieval after sync
            taskFeature: "event"
          },
          syncOptions: {
            syncIdField: "taskBarFeature"
          }
        },
        ...data.wrapperChildren
      ],
      style: {
        top: data.top,
        [scheduler.rtl ? "right" : "left"]: data.left,
        // DomHelper appends px to dimensions when using numbers
        height: eventRecord.isMilestone ? "1em" : data.height,
        width: data.width,
        style: data.wrapperStyle || "",
        fontSize: eventRecord.isMilestone ? Math.min(data.width, 40) : null
      },
      dataset: {
        // assignmentId is set in this function conditionally
        resourceId: resourceRecord.id,
        eventId: data.eventId,
        // Not using eventRecord.id to distinguish between Event and ResourceTimeRange
        // Sync using assignment id for events and event id for ResourceTimeRanges
        syncId: assignmentRecord ? this.assignmentStore.getOccurrence(assignmentRecord, eventRecord).id : data.eventId
      },
      // Will not be part of DOM, but attached to the element
      elementData: eventData,
      // Dragging etc. flags element as retained, to not reuse/release it during that operation. Events
      // always use assignments, but ResourceTimeRanges does not
      retainElement: (assignmentRecord || eventRecord).instanceMeta(this.scheduler).retainElement,
      // Options for this level of sync, lower levels can have their own
      syncOptions: {
        syncIdField: "taskFeature",
        // Remove instead of release when a feature is disabled
        releaseThreshold: 0
      }
    };
    elementConfig.className["b-sch-vertical"] = 1;
    if (data.zIndex) {
      elementConfig.style.zIndex = data.zIndex;
    }
    if (assignmentRecord) {
      elementConfig.dataset.assignmentId = assignmentRecord.id;
    }
    data.elementConfig = eventData.elementConfig = elementConfig;
    scheduler.afterRenderEvent({ renderData: data, domConfig: elementConfig });
    return elementConfig;
  }
  renderResource(resourceRecord, startDate, endDate) {
    var _a5, _b2, _c2;
    const me = this, topDateMS = (_a5 = startDate == null ? void 0 : startDate.getTime()) != null ? _a5 : me.topDateMS, bottomDateMS = (_b2 = endDate == null ? void 0 : endDate.getTime()) != null ? _b2 : me.bottomDateMS, eventDOMConfigs = [];
    let resourceEntry = me.resourceMap.get(resourceRecord.id);
    if (!resourceEntry) {
      resourceEntry = me.layoutResourceEvents(resourceRecord, startDate, endDate);
    }
    for (const eventId in resourceEntry) {
      const eventData = resourceEntry[eventId], { endDateMS, startDateMS, eventRecord } = eventData.renderData;
      if (
        // Only collect configs for those actually in view
        endDateMS >= topDateMS && startDateMS <= bottomDateMS && // And not being dragged, those have a temporary element already
        !eventRecord.instanceMeta(me.scheduler).hasTemporaryDragElement
      ) {
        const domConfig = ((_c2 = eventData.elementConfig) == null ? void 0 : _c2.className) !== "b-released" && eventData.elementConfig || me.renderEvent(eventData);
        eventDOMConfigs.push(domConfig);
      }
    }
    return eventDOMConfigs;
  }
  get shouldWaitForInitializeAndEngineReady() {
    return !this.initialized || !this.scheduler.isEngineReady && !this.scheduler.isCreating;
  }
  // Single cell so only one call to this renderer, determine which events are in view and draw them.
  // Drawing on scroll is triggered by `updateFromVerticalScroll()` and `updateFromHorizontalScroll()`
  renderer() {
    const me = this, { scheduler } = me, { topDate, bottomDate } = me.dateRange, syncConfigs = [], featureDomConfigs = [], visibleDomConfigs = { event: [] }, { allResourceRecords, fromScroll } = me;
    let { first: firstResource, last: lastResource } = me.resourceRange;
    if (me.shouldWaitForInitializeAndEngineReady) {
      return;
    }
    if (!DateHelper.isEqual(topDate, me.topDate) || !DateHelper.isEqual(bottomDate, me.bottomDate)) {
      me.topDate = topDate;
      me.bottomDate = bottomDate;
      me.topDateMS = topDate.getTime();
      me.bottomDateMS = bottomDate.getTime();
      const range = me.timeView.range = { startDate: topDate, endDate: bottomDate };
      scheduler.internalOnVisibleDateRangeChange(range);
    }
    if (firstResource !== -1 && lastResource !== -1) {
      if (scheduler.internalEventLayout.type === "layoutFn" && firstResource !== void 0) {
        me.renderResourceCustomLayout(syncConfigs);
      } else {
        for (let i = firstResource; i <= lastResource; i++) {
          syncConfigs.push.apply(syncConfigs, me.renderResource(allResourceRecords[i]));
        }
      }
      const { resourceRange } = me;
      firstResource = resourceRange.first;
      lastResource = resourceRange.last;
    }
    scheduler.getForegroundDomConfigs(featureDomConfigs);
    syncConfigs.push.apply(syncConfigs, featureDomConfigs);
    for (const domConfig of syncConfigs) {
      const { entityName = "event" } = domConfig.elementData.renderData;
      if (!visibleDomConfigs[entityName]) {
        visibleDomConfigs[entityName] = [];
      }
      visibleDomConfigs[entityName].push(domConfig);
    }
    for (const entityName in visibleDomConfigs) {
      DomSync.sync({
        domConfig: {
          onlyChildren: true,
          children: visibleDomConfigs[entityName]
        },
        targetElement: scheduler.canvases[entityName],
        syncIdField: "syncId",
        releaseThreshold: scheduler.eventReleaseThreshold,
        // Called by DomHelper when it creates, releases or reuses elements
        callback({ action, domConfig, lastDomConfig, targetElement, jsx }) {
          verticalRenderingDomSyncCallback(scheduler, fromScroll, {
            domConfig,
            jsx,
            action,
            targetElement,
            lastDomConfig
          });
        }
      });
    }
    if (me.firstResource !== firstResource || me.lastResource !== lastResource) {
      const range = me.resourceColumns.visibleResources = { firstResource, lastResource };
      me.firstResource = firstResource;
      me.lastResource = lastResource;
      scheduler.onVisibleResourceRangeChange(range);
      scheduler.trigger("resourceRangeChange", range);
    }
    this.fromScroll = false;
  }
  refresh(shouldAnimate) {
    this.scheduler.runWithTransition(() => this.renderer(), shouldAnimate);
  }
  // To match horizontals API, used from EventDrag
  refreshResources(resourceIds) {
    resourceIds = resourceIds.map((r) => {
      var _a5;
      return (_a5 = r.id) != null ? _a5 : r;
    });
    this.clearResources(resourceIds);
    this.refresh();
  }
  // To match horizontals API, used from EventDrag
  refreshEventsForResource(recordOrRow, force = true, draw = true) {
    this.refreshResources([recordOrRow.id]);
  }
  onRenderDone() {
  }
  //endregion
  //region Other
  get timeView() {
    return this.scheduler.timeView;
  }
  //endregion
  //region Cache
  // Clears cached resource layout
  clearResources(resourceIds) {
    const { resourceMap, eventMap } = this;
    resourceIds.forEach((resourceId) => {
      if (resourceMap.has(resourceId)) {
        Object.values(resourceMap.get(resourceId)).forEach(({ renderData: { eventId } }) => {
          delete eventMap.get(eventId)[resourceId];
        });
        resourceMap.delete(resourceId);
      }
    });
  }
  clearAll() {
    this.resourceMap.clear();
    this.eventMap.clear();
  }
  //endregion
};
function verticalRenderingDomSyncCallback(scheduler, fromScroll, {
  domConfig,
  jsx,
  action,
  targetElement,
  lastDomConfig
}) {
  var _a5, _b2, _c2;
  const { reactComponent } = scheduler;
  if (scheduler.isEventElement(domConfig) || jsx || ((_a5 = domConfig == null ? void 0 : domConfig.elementData) == null ? void 0 : _a5.jsx)) {
    const isRelease = releaseEventActions2[action], isRender = renderEventActions2[action];
    if ((_b2 = scheduler.processEventContent) == null ? void 0 : _b2.call(scheduler, {
      action,
      domConfig,
      isRelease: false,
      targetElement,
      reactComponent,
      jsx,
      scrolling: fromScroll
    }))
      return;
    if (isRelease && scheduler.isEventElement(lastDomConfig) && !lastDomConfig.isReleased) {
      const data = lastDomConfig.elementData.renderData, event = {
        renderData: data,
        assignmentRecord: data.assignmentRecord,
        eventRecord: data.eventRecord,
        resourceRecord: data.resourceRecord,
        element: targetElement
      };
      (_c2 = scheduler.processEventContent) == null ? void 0 : _c2.call(scheduler, {
        isRelease,
        domConfig: lastDomConfig,
        targetElement,
        reactComponent
      });
      if (targetElement === DomHelper.getActiveElement(targetElement)) {
        scheduler.focusElement.focus();
      }
      scheduler.trigger("releaseEvent", event);
    }
    if (isRender) {
      const data = domConfig.elementData.renderData, event = {
        renderData: data,
        assignmentRecord: data.assignmentRecord,
        eventRecord: data.eventRecord,
        resourceRecord: data.resourceRecord,
        element: targetElement,
        isReusingElement: action === "reuseElement",
        isRepaint: action === "reuseOwnElement"
      };
      event.reusingElement = action === "reuseElement";
      scheduler.trigger("renderEvent", event);
    }
  }
}
VerticalRendering._$name = "VerticalRendering";

// ../Scheduler/lib/Scheduler/view/TimeAxisBase.js
function isLastLevel(level, levels) {
  return level === levels.length - 1;
}
function isLastCell(level, cell) {
  return cell === level.cells[level.cells.length - 1];
}
var TimeAxisBase = class extends Widget {
  constructor() {
    super(...arguments);
    __publicField(this, "startDate", null);
    __publicField(this, "endDate", null);
    __publicField(this, "levels", []);
    __publicField(this, "size", null);
  }
  // Set visible date range
  set range({ startDate, endDate }) {
    var _a5;
    const me = this;
    if (me.startDate - startDate || me.endDate - endDate) {
      const { client } = me;
      me.startDate = startDate;
      me.endDate = endDate;
      if (me.sizeProperty === "width" && (client == null ? void 0 : client.hideHeaders) || me.sizeProperty === "height" && ((_a5 = client == null ? void 0 : client.verticalTimeAxisColumn) == null ? void 0 : _a5.hidden)) {
        return;
      }
      me.refresh();
    }
  }
  //endregion
  //region Html & rendering
  // Generates element configs for all levels defined by the current ViewPreset
  buildCells(start = this.startDate, end = this.endDate) {
    var _a5;
    const me = this, { sizeProperty } = me, {
      stickyHeaders,
      isVertical
    } = me.client || {}, featureHeaderConfigs = [], { length } = me.levels;
    const cellConfigs = me.levels.map((level, i) => {
      var _a6;
      const stickyHeader = stickyHeaders && (isVertical || i < length - 1);
      return {
        className: {
          "b-sch-header-row": 1,
          [`b-sch-header-row-${level.position}`]: 1,
          "b-sch-header-row-main": i === me.model.viewPreset.mainHeaderLevel,
          "b-lowest": isLastLevel(i, me.levels),
          "b-sticky-header": stickyHeader
        },
        syncOptions: {
          // Keep a maximum of 5 released cells. Might be fine with fewer since ticks are fixed width.
          // Prevents an unnecessary amount of cells from sticking around when switching from narrow to
          // wide tickSizes
          releaseThreshold: 5,
          syncIdField: "tickIndex"
        },
        dataset: {
          headerFeature: `headerRow${i}`,
          headerPosition: level.position
        },
        // Only include cells in view
        children: (_a6 = level.cells) == null ? void 0 : _a6.filter((cell) => cell.start < end && cell.end > start).map((cell, j) => ({
          role: "presentation",
          className: {
            "b-sch-header-timeaxis-cell": 1,
            [cell.headerCellCls]: cell.headerCellCls,
            [`b-align-${cell.align}`]: cell.align,
            "b-last": isLastCell(level, cell)
          },
          dataset: {
            tickIndex: cell.index,
            // Used in export tests to resolve dates from tick elements
            ...globalThis.DEBUG && { date: cell.start.getTime() }
          },
          style: {
            // DomHelper appends px to numeric dimensions
            [me.positionProperty]: cell.coord,
            [sizeProperty]: cell.width,
            [`min-${sizeProperty}`]: cell.width
          },
          children: [
            {
              tag: "span",
              role: "presentation",
              className: {
                "b-sch-header-text": 1,
                "b-sticky-header": stickyHeader
              },
              html: cell.value
            }
          ]
        }))
      };
    });
    (_a5 = me.client) == null ? void 0 : _a5.getHeaderDomConfigs(featureHeaderConfigs);
    cellConfigs.push(...featureHeaderConfigs);
    return {
      onlyChildren: true,
      syncOptions: {
        // Do not keep entire levels no longer used, for example after switching view preset
        releaseThreshold: 0
      },
      children: cellConfigs
    };
  }
  render(targetElement) {
    super.render(targetElement);
    this.refresh(true);
  }
  /**
   * Refresh the UI
   * @param {Boolean} [rebuild] Specify `true` to force a rebuild of the underlying header level definitions
   */
  refresh(rebuild = !this.levels.length) {
    const me = this, { columnConfig } = me.model, { levels } = me, oldLevelsCount = levels.length;
    if (rebuild) {
      levels.length = 0;
      columnConfig.forEach((cells, position) => levels[position] = {
        position,
        cells
      });
      me.size = levels[0].cells.reduce((sum, cell) => sum + cell.width, 0);
      const { parentElement } = me.element;
      if (parentElement && (levels.length !== oldLevelsCount || rebuild)) {
        parentElement.classList.remove(`b-sch-timeaxiscolumn-levels-${oldLevelsCount}`);
        parentElement.classList.add(`b-sch-timeaxiscolumn-levels-${levels.length}`);
      }
    }
    if (!me.startDate || !me.endDate) {
      return;
    }
    DomSync.sync({
      domConfig: me.buildCells(),
      targetElement: me.element,
      syncIdField: "headerFeature"
    });
    me.trigger("refresh");
  }
  //endregion
  // Our widget class doesn't include "base".
  get widgetClass() {
    return "b-timeaxis";
  }
};
__publicField(TimeAxisBase, "$name", "TimeAxisBase");
//region Config
__publicField(TimeAxisBase, "configurable", {
  /**
   * The minimum width for a bottom row header cell to be considered 'compact', which adds a special CSS class
   * to the row (for special styling). Copied from Scheduler/Gantt.
   * @config {Number}
   * @default
   */
  compactCellWidthThreshold: 15,
  // TimeAxisViewModel
  model: null,
  cls: null,
  /**
   * Style property to use as cell size. Either width or height depending on orientation
   * @config {'width'|'height'}
   * @private
   */
  sizeProperty: null,
  /**
   * Style property to use as cells position. Either left or top depending on orientation
   * @config {'left'|'top'}
   * @private
   */
  positionProperty: null
});
TimeAxisBase._$name = "TimeAxisBase";

// ../Scheduler/lib/Scheduler/view/HorizontalTimeAxis.js
var HorizontalTimeAxis = class extends TimeAxisBase {
  //endregion
  get positionProperty() {
    return "insetInlineStart";
  }
  get width() {
    return this.size;
  }
  onModelUpdate() {
    var _a5;
    if (!((_a5 = this.owner) == null ? void 0 : _a5.hideHeaders) && this.model.availableSpace > 0 && this.model.availableSpace !== this.width) {
      this.refresh(true);
    }
  }
  updateModel(timeAxisViewModel) {
    this.detachListeners("tavm");
    timeAxisViewModel == null ? void 0 : timeAxisViewModel.ion({
      name: "tavm",
      update: "onModelUpdate",
      thisObj: this
    });
  }
};
//region Config
__publicField(HorizontalTimeAxis, "$name", "HorizontalTimeAxis");
__publicField(HorizontalTimeAxis, "type", "horizontaltimeaxis");
__publicField(HorizontalTimeAxis, "configurable", {
  model: null,
  sizeProperty: "width"
});
HorizontalTimeAxis._$name = "HorizontalTimeAxis";

// ../Scheduler/lib/Scheduler/view/ResourceHeader.js
var ResourceHeader = class extends Widget {
  constructor() {
    super(...arguments);
    /**
     * An index of the first visible resource in vertical mode
     * @property {Number}
     * @readonly
     * @private
     */
    __publicField(this, "firstResource", -1);
    /**
     * An index of the last visible resource in vertical mode
     * @property {Number}
     * @readonly
     * @private
     */
    __publicField(this, "lastResource", -1);
  }
  //endregion
  //region Init
  construct(config) {
    const me = this;
    config.scheduler._resourceColumns = me;
    super.construct(config);
    if (me.imagePath != null) {
      me.element.classList.add("b-has-images");
    }
    EventHelper.on({
      element: me.element,
      delegate: ".b-resourceheader-cell",
      capture: true,
      click: "onResourceMouseEvent",
      dblclick: "onResourceMouseEvent",
      contextmenu: "onResourceMouseEvent",
      thisObj: me
    });
  }
  changeShowAvatars(show) {
    var _a5;
    (_a5 = this.avatarRendering) == null ? void 0 : _a5.destroy();
    if (show) {
      this.avatarRendering = new AvatarRendering({
        element: this.element
      });
    }
    return show;
  }
  updateShowAvatars() {
    if (!this.isConfiguring) {
      this.refresh();
    }
  }
  //endregion
  //region ResourceStore
  updateResourceStore(store) {
    const me = this;
    me.detachListeners("resourceStore");
    if (store) {
      store.ion({
        name: "resourceStore",
        changePreCommit: "onResourceStoreDataChange",
        thisObj: me
      });
      if (store.count) {
        me.onResourceStoreDataChange({});
      }
    }
  }
  // Redraw resource headers on any data change
  onResourceStoreDataChange({ action }) {
    const me = this;
    me.getConfig("fillWidth");
    me.getConfig("fitWidth");
    me.updateWidthCache();
    const {
      element
    } = me, width = me.totalWidth;
    if (me.scheduler.variableColumnWidths) {
      me._fillWidth = me._fitWidth = false;
    } else {
      me._fillWidth = me.configuredFillWidth;
      me._fitWidth = me.configuredFitWidth;
    }
    if (width !== me.width) {
      DomHelper.setLength(element, "width", width);
      me.column.set("width", width, me.column.grid.isConfiguring);
    }
    if (action === "removeall") {
      element.innerHTML = "";
    }
    if (action === "remove" || action === "add" || action === "filter" || me.fitWidth || me.fillWidth) {
      me.refreshWidths();
    }
    me.column.grid.toggleEmptyText();
  }
  get totalWidth() {
    return this.updateWidthCache();
  }
  updateWidthCache() {
    let result = 0;
    const { scheduler } = this;
    scheduler.variableColumnWidths = false;
    scheduler.resourceStore.forEach((resource) => {
      const instanceMeta = resource.instanceMeta(scheduler);
      if (instanceMeta.columnWidth == null && resource.columnWidth) {
        instanceMeta.columnWidth = resource.columnWidth;
      }
      instanceMeta.insetStart = result;
      instanceMeta.insetEnd = result + scheduler.getResourceWidth(resource);
      if (instanceMeta.columnWidth == null) {
        result += scheduler.resourceColumnWidth;
      } else {
        result += instanceMeta.columnWidth;
        scheduler.variableColumnWidths = true;
      }
    });
    return result;
  }
  //endregion
  //region Properties
  changeColumnWidth(columnWidth) {
    if (!this.refreshingWidths) {
      this.configuredColumnWidth = columnWidth;
    }
    return columnWidth;
  }
  updateColumnWidth(width, oldWidth) {
    const me = this;
    if (!me.refreshingWidths) {
      me.refreshWidths();
    }
    if (!me.isConfiguring) {
      if (me.resourceStore.isGrouped) {
        me.updateWidthCache();
      }
      me.refresh();
      me.trigger("columnWidthChange", { width, oldWidth });
    }
  }
  changeFillWidth(fillWidth) {
    return this.configuredFillWidth = fillWidth;
  }
  updateFillWidth() {
    if (!this.isConfiguring) {
      this.refreshWidths();
    }
  }
  changeFitWidth(fitWidth) {
    return this.configuredFitWidth = fitWidth;
  }
  updateFitWidth() {
    if (!this.isConfiguring) {
      this.refreshWidths();
    }
  }
  getImageURL(imageName) {
    return StringHelper.joinPaths([this.imagePath || "", imageName || ""]) + ((imageName == null ? void 0 : imageName.includes(".")) ? "" : this.imageExtension);
  }
  updateImagePath() {
    if (!this.isConfiguring) {
      this.refresh();
    }
  }
  //endregion
  //region Fit to width
  updateAvailableWidth(width) {
    this.refreshWidths();
  }
  // Updates the column widths according to fill and fit settings
  refreshWidths() {
    var _a5;
    const me = this, {
      availableWidth,
      configuredColumnWidth
    } = me, count = (_a5 = me.resourceStore) == null ? void 0 : _a5.count;
    if (!availableWidth || !count || me.scheduler.variableColumnWidths) {
      return;
    }
    me.refreshingWidths = true;
    const fit = me.fitWidth || me.fillWidth && configuredColumnWidth * count < availableWidth, useWidth = fit ? Math.floor(availableWidth / count) : configuredColumnWidth, shouldAnimate = me.column.grid.enableEventAnimations && Math.abs(me._columnWidth - useWidth) > 30;
    DomHelper.addTemporaryClass(me.element, "b-animating", shouldAnimate ? 300 : 0, me);
    me.columnWidth = useWidth;
    me.refreshingWidths = false;
  }
  //endregion
  //region Rendering
  // Visual resource range, set by VerticalRendering + its buffer
  set visibleResources({ firstResource, lastResource }) {
    this.firstResource = firstResource;
    this.lastResource = lastResource;
    this.updateWidthCache();
    this.refresh();
  }
  /**
   * Refreshes the visible headers
   */
  refresh() {
    const me = this, {
      firstResource,
      scheduler,
      resourceStore,
      lastResource
    } = me, groupField = resourceStore.isGrouped && resourceStore.groupers[0].field;
    me.element.classList.toggle("b-grouped", Boolean(groupField));
    const configs = me.renderResourceHeaders(firstResource, lastResource);
    DomSync.sync({
      domConfig: {
        onlyChildren: true,
        children: configs
      },
      targetElement: me.element,
      syncIdField: "resourceId",
      callback: ({ jsx, targetElement, domConfig }) => {
        var _a5;
        (_a5 = scheduler.processResourceHeader) == null ? void 0 : _a5.call(scheduler, { jsx, targetElement, domConfig });
      }
    });
  }
  renderResourceHeaders(firstResource, lastResource) {
    var _a5, _b2;
    const me = this, {
      scheduler,
      resourceStore
    } = me, {
      variableColumnWidths
    } = scheduler, groupField = resourceStore.isGrouped && resourceStore.groupers[0].field, result = [];
    if (!me.column.grid.isConfiguring && firstResource > -1 && lastResource > -1 && lastResource < resourceStore.count) {
      let currentGroup;
      for (let i = firstResource; i <= lastResource; i++) {
        const resourceRecord = resourceStore.allResourceRecords[i], groupRecord = resourceRecord.instanceMeta(resourceStore).groupParent, groupChildren = groupRecord == null ? void 0 : groupRecord.groupChildren;
        if (groupField && groupRecord.id !== (currentGroup == null ? void 0 : currentGroup.dataset.resourceId)) {
          const groupLeft = groupChildren[0].instanceMeta(scheduler).insetStart, groupWidth = groupChildren[groupChildren.length - 1].instanceMeta(scheduler).insetEnd - groupLeft, groupRenderer = (_a5 = scheduler.features.group) == null ? void 0 : _a5.renderer;
          let text = groupChildren[0][groupField], prop = "text";
          if (groupRenderer) {
            const groupRecord2 = resourceRecord.instanceMeta(resourceStore).groupParent, meta = groupRecord2.meta, groupRowFor = meta.groupRowFor, count = meta.childCount;
            text = scheduler.features.group.groupRenderer({
              groupRowFor,
              groupRecords: groupRecord2.groupChildren,
              count
            });
            if (typeof text === "string" && text.includes("<")) {
              prop = "html";
            } else if (typeof text === "object") {
              prop = "children";
              text = [text];
            }
          }
          currentGroup = {
            className: "b-resourceheader-group-cell",
            dataset: {
              resourceId: groupRecord.id
            },
            style: {
              left: groupLeft,
              width: groupWidth
            },
            children: [
              {
                className: "b-resourceheader-group-text-wrap",
                children: Array.isArray(text) ? text : [
                  {
                    tag: "span",
                    [prop]: text
                  }
                ]
              },
              {
                className: "b-resourceheader-group-children",
                children: []
              }
            ]
          };
          result.push(currentGroup);
        }
        const instanceMeta = resourceRecord.instanceMeta(scheduler), width = instanceMeta.columnWidth || me.columnWidth, position = groupField ? instanceMeta.insetStart - currentGroup.style.left : variableColumnWidths ? instanceMeta.insetStart : i * me.columnWidth, elementConfig = {
          tabIndex: 0,
          // Might look like overkill to use DomClassList here, but can be used in headerRenderer
          className: new DomClassList({
            "b-resourceheader-cell": 1
          }),
          dataset: {
            resourceId: resourceRecord.id
          },
          style: {
            [scheduler.rtl ? "right" : "left"]: position,
            width
          },
          children: []
        };
        if (me.headerRenderer) {
          const value2 = me.headerRenderer({ elementConfig, resourceRecord });
          if (value2) {
            if (typeof value2 === "string") {
              elementConfig.html = value2;
            } else if (typeof value2 === "object") {
              elementConfig.children = [value2];
            }
          }
        } else {
          let imageUrl;
          if (resourceRecord.imageUrl) {
            imageUrl = resourceRecord.imageUrl;
          } else {
            if (me.imagePath != null) {
              if (resourceRecord.image !== false) {
                const imageName = resourceRecord.image || ((_b2 = resourceRecord.name) == null ? void 0 : _b2.toLowerCase());
                imageUrl = imageName && me.getImageURL(imageName);
              }
            }
          }
          elementConfig.children.push(
            me.showAvatars && me.avatarRendering.getResourceAvatar({
              resourceRecord,
              initials: resourceRecord.initials,
              color: resourceRecord.eventColor,
              iconCls: resourceRecord.iconCls,
              defaultImageUrl: me.defaultImageName && me.getImageURL(me.defaultImageName),
              imageUrl
            }),
            {
              tag: "span",
              className: "b-resource-name",
              html: StringHelper.encodeHtml(resourceRecord.name)
            }
          );
        }
        if (groupField) {
          currentGroup.children[1].children.push(elementConfig);
        } else {
          result.push(elementConfig);
        }
      }
    }
    return result;
  }
  //endregion
  onResourceMouseEvent(event) {
    const resourceCell = event.target.closest(".b-resourceheader-cell"), resourceRecord = this.resourceStore.getById(resourceCell.dataset.resourceId);
    this.trigger("resourceHeader" + StringHelper.capitalize(event.type), {
      resourceRecord,
      event
    });
  }
  // This function is not meant to be called by any code other than Base#getCurrentConfig().
  // It extracts the current configs for the header, removing irrelevant ones
  getCurrentConfig(options) {
    const result = super.getCurrentConfig(options);
    delete result.resourceStore;
    delete result.column;
    delete result.type;
    return result;
  }
};
//region Config
__publicField(ResourceHeader, "$name", "ResourceHeader");
__publicField(ResourceHeader, "type", "resourceheader");
__publicField(ResourceHeader, "configurable", {
  /**
   * Resource store used to render resource headers. Assigned from Scheduler.
   * @config {Scheduler.data.ResourceStore}
   * @private
   */
  resourceStore: null,
  /**
   * Custom header renderer function. Can be used to manipulate the element config used to create the element
   * for the header:
   *
   * ```javascript
   * new Scheduler({
   *   resourceColumns : {
   *     headerRenderer({ elementConfig, resourceRecord }) {
   *       elementConfig.dataset.myExtraData = 'extra';
   *       elementConfig.style.fontWeight = 'bold';
   *     }
   *   }
   * });
   * ```
   *
   * See {@link DomConfig} for more information.
   *
   * <div class="note">
   * You should never modify any records inside this method.
   * </div>
   *
   * Or as a template by returning HTML from the function:
   *
   * ```javascript
   * new Scheduler({
   *   resourceColumns : {
   *     headerRenderer : ({ resourceRecord }) => `
   *       <div class="my-custom-template">
   *       ${resourceRecord.firstName} {resourceRecord.surname}
   *       </div>
   *     `
   *   }
   * });
   * ```
   *
   * <div class="note">
   * When using `headerRenderer` no default internal markup is applied to the resource header cell,
   * `iconCls` and {@link Scheduler.model.ResourceModel#field-imageUrl} or {@link Scheduler.model.ResourceModel#field-image}
   * will have no effect unless you supply custom markup for them.
   * </div>
   *
   * @config {Function}
   * @param {Object} params Object containing the params below
   * @param {Scheduler.model.ResourceModel} params.resourceRecord Resource whose header is being rendered
   * @param {DomConfig} params.elementConfig A config object used to create the element for the resource
   * @returns {String} The raw HTML string to render into the header. Remember to HTML encode your content!
   */
  headerRenderer: null,
  /**
   * Set to `false` to render just the resource name, `true` to render an avatar (or initials if no image exists)
   * @config {Boolean}
   * @default true
   */
  showAvatars: {
    value: true,
    $config: "nullify"
  },
  /**
   * Assign to toggle resource columns **fill* mode. `true` means they will stretch (grow) to fill viewport, `false`
   * that they will respect their configured `columnWidth`.
   *
   * This is ignored if *any* resources are loaded with {@link Scheduler.model.ResourceModel#field-columnWidth}.
   * @member {Boolean} fillWidth
   */
  /**
   * Automatically resize resource columns to **fill** available width. Set to `false` to always respect the
   * configured `columnWidth`.
   *
   * This is ignored if *any* resources are loaded with {@link Scheduler.model.ResourceModel#field-columnWidth}.
   * @config {Boolean}
   * @default
   */
  fillWidth: true,
  /**
   * Assign to toggle resource columns **fit* mode. `true` means they will grow or shrink to always fit viewport,
   * `false` that they will respect their configured `columnWidth`.
   *
   * This is ignored if *any* resources are loaded with {@link Scheduler.model.ResourceModel#field-columnWidth}.
   * @member {Boolean} fitWidth
   */
  /**
   * Automatically resize resource columns to always **fit** available width.
   *
   * This is ignored if *any* resources are loaded with {@link Scheduler.model.ResourceModel#field-columnWidth}.
   * @config {Boolean}
   * @default
   */
  fitWidth: false,
  /**
   * Width for each resource column.
   *
   * This is used for resources which are not are loaded with a {@link Scheduler.model.ResourceModel#field-columnWidth}.
   * This config may be ignored if resources do not fill the view and {@link #config-fillWidth} is set to `true`.
   * @prp {Number}
   */
  columnWidth: 150,
  // Copied from Scheduler#resourceImagePath on creation in TimeAxisColumn.js
  imagePath: null,
  // Copied from Scheduler#resourceImageExtension on creation in TimeAxisColumn.js
  imageExtension: null,
  // Copied from Scheduler#defaultResourceImageName on creation in TimeAxisColumn.js
  defaultImageName: null,
  availableWidth: null
});
ResourceHeader._$name = "ResourceHeader";

// ../Scheduler/lib/Scheduler/column/TimeAxisColumn.js
var TimeAxisColumn = class extends Events_default(WidgetColumn) {
  constructor() {
    super(...arguments);
    //region Config
    __publicField(this, "headerHoverable", false);
  }
  static get defaults() {
    return {
      /**
       * Set to `false` to prevent this column header from being dragged.
       * @config {Boolean} draggable
       * @category Interaction
       * @default false
       */
      draggable: false,
      /**
       * Set to `false` to prevent grouping by this column.
       * @config {Boolean} groupable
       * @category Interaction
       * @default false
       */
      groupable: false,
      /**
       * Allow column visibility to be toggled through UI.
       * @config {Boolean} hideable
       * @default false
       * @category Interaction
       */
      hideable: false,
      /**
       * Show column picker for the column.
       * @config {Boolean} showColumnPicker
       * @default false
       * @category Menu
       */
      showColumnPicker: false,
      /**
       * Allow filtering data in the column (if Filter feature is enabled)
       * @config {Boolean} filterable
       * @default false
       * @category Interaction
       */
      filterable: false,
      /**
       * Allow sorting of data in the column
       * @config {Boolean} sortable
       * @category Interaction
       * @default false
       */
      sortable: false,
      /**
       * Set to `false` to prevent the column from being drag-resized when the ColumnResize plugin is enabled.
       * @config {Boolean} resizable
       * @default false
       * @category Interaction
       */
      resizable: false,
      /**
       * Allow searching in the column (respected by QuickFind and Search features)
       * @config {Boolean} searchable
       * @default false
       * @category Interaction
       */
      searchable: false,
      editor: false,
      /**
       * Set to `true` to show a context menu on the cell elements in this column
       * @config {Boolean} enableCellContextMenu
       * @default false
       * @category Menu
       */
      enableCellContextMenu: false,
      tooltipRenderer: false,
      /**
       * CSS class added to the header of this column
       * @config {String} cls
       * @category Rendering
       * @default 'b-sch-timeaxiscolumn'
       */
      cls: "b-sch-timeaxiscolumn",
      // needs to have width specified, flex-basis messes measurements up
      needWidth: true,
      ariaLabel: null,
      mode: null,
      region: "normal",
      exportable: false,
      htmlEncode: false,
      id: "timeaxiscolumn"
    };
  }
  //region Init
  construct(config) {
    const me = this;
    config.disableRendererWarning = true;
    super.construct(...arguments);
    me.thisObj = me;
    me.grid._timeAxisColumn = me;
    me.timeAxisViewModel = me.grid.timeAxisViewModel;
    me.mode = me.mode;
    me.grid.ion({
      paint: "onTimelinePaint",
      thisObj: me,
      once: true
    });
    LocaleManager_default.ion({ locale: me.updateHeaderAriaLabel, thisObj: me });
  }
  static get autoExposeFields() {
    return true;
  }
  // endregion
  updateHeaderAriaLabel() {
    const { grid } = this, { visibleDateRange } = grid, startDateStr = DateHelper.format(visibleDateRange == null ? void 0 : visibleDateRange.startDate, grid.displayDateFormat), endDateStr = DateHelper.format(visibleDateRange == null ? void 0 : visibleDateRange.endDate, grid.displayDateFormat);
    return super.updateHeaderAriaLabel(this.L(this.ariaLabel || "L{TimeAxisColumn.ariaLabel}")(startDateStr, endDateStr));
  }
  doDestroy() {
    var _a5, _b2;
    (_a5 = this.resourceColumns) == null ? void 0 : _a5.destroy();
    (_b2 = this.timeAxisView) == null ? void 0 : _b2.destroy();
    super.doDestroy();
  }
  set mode(mode) {
    const me = this, { grid } = me;
    me.set("mode", mode);
    if (mode === "horizontal") {
      me.timeAxisView = new HorizontalTimeAxis({
        model: me.timeAxisViewModel,
        compactCellWidthThreshold: me.compactCellWidthThreshold,
        owner: grid,
        client: grid
      });
    } else if (mode === "vertical") {
      me.resourceColumns = ResourceHeader.new({
        column: me,
        scheduler: grid,
        resourceStore: grid.resourceStore,
        imagePath: grid.resourceImagePath,
        imageExtension: grid.resourceImageExtension,
        defaultImageName: grid.defaultResourceImageName
      }, grid.resourceColumns || {});
      me.relayEvents(me.resourceColumns, [
        "resourceheaderclick",
        "resourceheaderdblclick",
        "resourceheadercontextmenu"
      ]);
    }
  }
  get mode() {
    return this.get("mode");
  }
  //region Events
  onViewModelUpdate({ source: viewModel }) {
    const me = this;
    if (me.grid.timeAxisSubGrid.collapsed || viewModel.totalSize === 0) {
      return;
    }
    if (me.mode === "horizontal") {
      me.refreshHeader(true);
      me.width = viewModel.totalSize;
      me.grid.refresh();
      me.subGrid.refreshFakeScroll();
    } else if (me.mode === "vertical") {
      me.grid.refreshRows();
    }
  }
  // Called on paint. SubGrid has its width so this is the earliest time to configure the TimeAxisViewModel with
  // correct width
  onTimelinePaint({ firstPaint }) {
    var _a5;
    const me = this;
    if (!me.subGrid.insertRowsBefore) {
      return;
    }
    if (firstPaint) {
      me.updateHeaderAriaLabel();
      me.subGridElement.classList.add("b-timeline-subgrid");
      if (me.mode === "vertical") {
        me.refreshHeader();
        (_a5 = me.grid) == null ? void 0 : _a5.onHeightChange();
      }
    }
  }
  onCellFocus() {
    this.updateHeaderAriaLabel(this.localizeProperty("ariaLabel"));
  }
  //endregion
  //region Rendering
  /**
   * Refreshes the columns header contents (which is either a HorizontalTimeAxis or a ResourceHeader). Useful if you
   * have rendered some extra meta data that depends on external data such as the EventStore or ResourceStore.
   */
  refreshHeader(internal) {
    const me = this, { element } = me;
    if (element) {
      if (me.mode === "horizontal") {
        !internal && me.timeAxisViewModel.update(void 0, void 0, true);
        if (!me.timeAxisView.rendered) {
          element.querySelector(".b-grid-header-text").remove();
          me.timeAxisView.render(element);
        } else {
          me.timeAxisView.refresh(true);
        }
      } else if (me.mode === "vertical") {
        if (!me.resourceColumns.currentElement) {
          element.innerHTML = "";
          me.resourceColumns.render(element);
        } else {
          me.resourceColumns.refresh();
        }
      }
    }
  }
  defaultRenderer(renderData) {
    var _a5;
    const { grid } = this;
    if (grid.isTimelineHistogramBase) {
      return;
    }
    if (grid.project.isInitialCommitPerformed || grid.project.isDelayingCalculation || ((_a5 = grid.eventStore) == null ? void 0 : _a5.lazyLoad)) {
      grid.currentOrientation.renderer(renderData);
      return super.defaultRenderer(renderData);
    }
    return "";
  }
  isCellDirty(record) {
    return false;
  }
  //endregion
  get timeAxisViewModel() {
    return this._timeAxisViewModel;
  }
  set timeAxisViewModel(timeAxisViewModel) {
    const me = this;
    me.detachListeners("tavm");
    timeAxisViewModel == null ? void 0 : timeAxisViewModel.ion({
      name: "tavm",
      update: "onViewModelUpdate",
      prio: -1e4,
      thisObj: me
    });
    me._timeAxisViewModel = timeAxisViewModel;
    if (me.timeAxisView) {
      me.timeAxisView.model = timeAxisViewModel;
    }
  }
  // Width of the time axis column is solely determined by the zoom level. We should not keep it part of the state
  // otherwise restoring the state might break the normal zooming process.
  // Covered by SchedulerState.t
  // https://github.com/bryntum/support/issues/5545
  getState() {
    const state = super.getState();
    delete state.width;
    delete state.flex;
    return state;
  }
};
__publicField(TimeAxisColumn, "$name", "TimeAxisColumn");
__publicField(TimeAxisColumn, "type", "timeAxis");
__publicField(TimeAxisColumn, "fields", [
  // Exclude some irrelevant fields from getCurrentConfig()
  { name: "locked", persist: false },
  { name: "flex", persist: false },
  { name: "width", persist: false },
  { name: "cellCls", persist: false },
  { name: "field", persist: false },
  "mode"
]);
ColumnStore.registerColumnType(TimeAxisColumn);
TimeAxisColumn._$name = "TimeAxisColumn";

// ../Scheduler/lib/Scheduler/view/VerticalTimeAxis.js
var VerticalTimeAxis = class extends TimeAxisBase {
  static get configurable() {
    return {
      cls: "b-verticaltimeaxis",
      sizeProperty: "height",
      positionProperty: "top",
      wrapText: true
    };
  }
  // All cells overlayed in the same space.
  // For future use.
  buildHorizontalCells() {
    const me = this, { client } = me, stickyHeaders = client == null ? void 0 : client.stickyHeaders, featureHeaderConfigs = [], cellConfigs = me.levels.reduce((result, level, i) => {
      var _a5;
      if (level.cells) {
        result.push(...(_a5 = level.cells) == null ? void 0 : _a5.filter((cell) => cell.start < me.endDate && cell.end > me.startDate).map((cell, j, cells) => ({
          role: "presentation",
          className: {
            "b-sch-header-timeaxis-cell": 1,
            [cell.headerCellCls]: cell.headerCellCls,
            [`b-align-${cell.align}`]: cell.align,
            "b-last": j === cells.length - 1,
            "b-lowest": i === me.levels.length - 1
          },
          dataset: {
            tickIndex: cell.index,
            cellId: `${i}-${cell.index}`,
            headerPosition: i,
            // Used in export tests to resolve dates from tick elements
            ...globalThis.DEBUG && { date: cell.start.getTime() }
          },
          style: {
            // DomHelper appends px to numeric dimensions
            top: cell.coord,
            height: cell.width,
            minHeight: cell.width
          },
          children: [
            {
              role: "presentation",
              className: {
                "b-sch-header-text": 1,
                "b-sticky-header": stickyHeaders
              },
              html: cell.value
            }
          ]
        })));
      }
      return result;
    }, []);
    client == null ? void 0 : client.getHeaderDomConfigs(featureHeaderConfigs);
    cellConfigs.push(...featureHeaderConfigs);
    return {
      className: me.widgetClassList,
      dataset: {
        headerFeature: `headerRow0`,
        headerPosition: 0
      },
      syncOptions: {
        // Keep a maximum of 5 released cells. Might be fine with fewer since ticks are fixed width.
        // Prevents an unnecessary amount of cells from sticking around when switching from narrow to
        // wide tickSizes
        releaseThreshold: 5,
        syncIdField: "cellId"
      },
      children: cellConfigs
    };
  }
  get height() {
    return this.size;
  }
};
__publicField(VerticalTimeAxis, "$name", "VerticalTimeAxis");
VerticalTimeAxis._$name = "VerticalTimeAxis";

// ../Scheduler/lib/Scheduler/column/VerticalTimeAxisColumn.js
var VerticalTimeAxisColumn = class extends Column {
  static get defaults() {
    return {
      /**
       * @hideconfigs autoWidth, autoHeight, draggable, groupable, hideable, showColumnPicker,
       * sortable, searchable, editor, enableCellContextMenu, tooltipRenderer
       */
      draggable: false,
      groupable: false,
      hideable: false,
      showColumnPicker: false,
      filterable: false,
      sortable: false,
      searchable: false,
      editor: false,
      enableCellContextMenu: false,
      tooltipRenderer: false,
      /**
       * Column minimal width. If value is Number then minimal width is in pixels
       * @config {Number|String} minWidth
       * @default 0
       * @category Layout
       */
      minWidth: 0,
      resizable: false,
      cellCls: "b-verticaltimeaxiscolumn",
      locked: true,
      flex: 1,
      alwaysClearCell: false,
      ariaLabel: null
    };
  }
  updateHeaderAriaLabel() {
    const { grid } = this, { visibleDateRange } = grid, startDateStr = DateHelper.format(visibleDateRange == null ? void 0 : visibleDateRange.startDate, grid.displayDateFormat), endDateStr = DateHelper.format(visibleDateRange == null ? void 0 : visibleDateRange.endDate, grid.displayDateFormat);
    return super.updateHeaderAriaLabel(this.L(this.ariaLabel || "L{TimeAxisColumn.ariaLabel}")(startDateStr, endDateStr));
  }
  get isFocusable() {
    return true;
  }
  construct(data) {
    const me = this;
    super.construct(...arguments);
    me.view = new VerticalTimeAxis({
      model: me.grid.timeAxisViewModel,
      client: me.grid
    });
    me.grid.ion({
      paint: "onTimelinePaint",
      thisObj: me,
      once: true
    });
    LocaleManager_default.ion({ locale: me.updateHeaderAriaLabel, thisObj: me });
  }
  defaultRenderer({ cellElement, size }) {
    this.view.render(cellElement);
    size.height = this.view.height;
  }
  // This function is not meant to be called by any code other than Base#getCurrentConfig().
  // It extracts the current configs (fields) for the column, removing irrelevant ones
  getCurrentConfig(options) {
    const result = super.getCurrentConfig(options);
    delete result.id;
    delete result.region;
    delete result.type;
    delete result.field;
    delete result.ariaLabel;
    delete result.cellAriaLabel;
    return result;
  }
  onTimelinePaint({ firstPaint }) {
    if (firstPaint) {
      this.updateHeaderAriaLabel();
    }
  }
};
__publicField(VerticalTimeAxisColumn, "$name", "VerticalTimeAxisColumn");
__publicField(VerticalTimeAxisColumn, "type", "verticalTimeAxis");
ColumnStore.registerColumnType(VerticalTimeAxisColumn);
VerticalTimeAxisColumn._$name = "VerticalTimeAxisColumn";

// ../Scheduler/lib/Scheduler/view/SchedulerBase.js
var descriptionFormats = {
  month: "MMMM, YYYY",
  week: ["MMMM YYYY (Wp)", "S{MMM} - E{MMM YYYY} (S{Wp})"],
  day: "MMMM D, YYYY"
};
var compareRange = (r1, r2) => r1 && r2 ? r1.unit === r2.unit && r1.magnitude === r2.magnitude : r1 === r2;
var emptyObject14 = Object.freeze({});
var _SchedulerBase = class _SchedulerBase extends TimelineBase.mixin(
  CrudManagerView_default,
  LazyLoadView_default,
  Describable_default,
  SchedulerDom_default,
  SchedulerDomEvents_default,
  SchedulerStores_default,
  SchedulerScroll_default,
  SchedulerState_default,
  SchedulerEventRendering_default,
  SchedulerRegions_default,
  EventSelection_default,
  EventNavigation_default,
  CurrentConfig_default,
  TransactionalFeatureMixin_default
) {
  constructor() {
    super(...arguments);
    __publicField(this, "timeCellSelector", ".b-sch-timeaxis-cell");
    __publicField(this, "resourceTimeRangeSelector", ".b-sch-resourcetimerange");
  }
  static get defaultConfig() {
    return {
      /**
       * Scheduler mode. Supported values: horizontal, vertical
       * @config {'horizontal'|'vertical'} mode
       * @default
       * @category Common
       * @non-lazy-load
       */
      mode: "horizontal",
      /**
       * CSS class to add to rendered events
       * @config {String}
       * @category CSS
       * @private
       * @default
       */
      eventCls: "b-sch-event",
      /**
       * CSS class to add to cells in the timeaxis column
       * @config {String}
       * @category CSS
       * @private
       * @default
       */
      timeCellCls: "b-sch-timeaxis-cell",
      /**
       * A CSS class to apply to each event in the view on mouseover (defaults to 'b-sch-event-hover').
       * @config {String}
       * @default
       * @category CSS
       * @private
       */
      overScheduledEventClass: "b-sch-event-hover",
      /**
       * Set to `false` if you don't want to allow events overlapping times for any one resource (defaults to `true`).
       * <div class="note">Note that toggling this at runtime won't affect already overlapping events.</div>
       *
       * @prp {Boolean}
       * @default
       * @category Scheduled events
       */
      allowOverlap: true,
      /**
       * The height in pixels of Scheduler rows.
       * @config {Number}
       * @default
       * @category Common
       */
      rowHeight: 60,
      /**
       * Scheduler overrides Grid's default implementation of {@link Grid.view.GridBase#config-getRowHeight} to
       * pre-calculate row heights based on events in the rows.
       *
       * The amount of rows that are pre-calculated is limited for performance reasons. The limit is configurable
       * by specifying the {@link Scheduler.view.SchedulerBase#config-preCalculateHeightLimit} config.
       *
       * The results of the calculation are cached internally.
       *
       * {@note}Apps are not currently intended to override this logic, instead they can control height of
       * individual rows using a cell <code>renderer</code>. See the snippet below for an example.{/@note}
       *
       * ```javascript
       * new Scheduler({
       *     columns : [
       *         {
       *             text  : 'Name',
       *             field : 'name',
       *             width : 130,
       *             renderer({ value, record, size }) {
       *                 // Set your desired height here
       *                 size.height = record.parentIndex % 2 === 0 ? 100 : 40;
       *
       *                 return value;
       *             }
       *         }
       *     ]
       * });
       * ```
       *
       * {@note}Also note that the height set is used as the base height for the event layout used, for example if
       * stacking two events in a stack layout, the row will be twice as heigh (+ any margins).{/@note}
       *
       * @config {Function} getRowHeight
       * @param {Scheduler.model.ResourceModel} getRowHeight.record Resource record to determine row height for
       * @returns {Number} Desired row height
       * @category Layout
       */
      /**
       * Maximum number of resources for which height is pre-calculated. If you have many events per
       * resource you might want to lower this number to gain some initial rendering performance.
       *
       * Specify a falsy value to opt out of row height pre-calculation.
       *
       * @config {Number}
       * @default
       * @category Layout
       */
      preCalculateHeightLimit: 1e4,
      crudManagerClass: CrudManager,
      // Skip live redraw of events while dragging RegionResize splitter if too many events are rendered in DOM
      liveRedrawWhileMovingSplitterThreshold: 100,
      eventContainerSelector: ".b-timeaxissubgrid",
      testConfig: {
        loadMaskError: {
          autoClose: 10,
          showDelay: 0
        }
      }
    };
  }
  //endregion
  //region Store & model docs
  // Documented here instead of in SchedulerStores since SchedulerPro uses different types
  // Configs
  /**
   * Inline events, will be loaded into an internally created EventStore.
   * @config {Scheduler.model.EventModel[]|Scheduler.model.EventModelConfig[]} events
   * @non-lazy-load
   * @category Data
   */
  /**
   * The {@link Scheduler.data.EventStore} holding the events to be rendered into the scheduler (required).
   * @config {Scheduler.data.EventStore|Scheduler.data.EventStoreConfig} eventStore
   * @category Data
   */
  /**
   * Inline resources, will be loaded into an internally created ResourceStore.
   * @config {Scheduler.model.ResourceModel[]|Scheduler.model.ResourceModelConfig[]} resources
   * @non-lazy-load
   * @category Data
   */
  /**
   * The {@link Scheduler.data.ResourceStore} holding the resources to be rendered into the scheduler (required).
   * @config {Scheduler.data.ResourceStore|Scheduler.data.ResourceStoreConfig} resourceStore
   * @category Data
   */
  /**
   * Inline assignments, will be loaded into an internally created AssignmentStore.
   * @config {Scheduler.model.AssignmentModel[]|Object[]} assignments
   * @non-lazy-load
   * @category Data
   */
  /**
   * The optional {@link Scheduler.data.AssignmentStore}, holding assignments between resources and events.
   * Required for multi assignments.
   * @config {Scheduler.data.AssignmentStore|Scheduler.data.AssignmentStoreConfig} assignmentStore
   * @category Data
   */
  /**
   * Inline dependencies, will be loaded into an internally created DependencyStore.
   * @config {Scheduler.model.DependencyModel[]|Scheduler.model.DependencyModelConfig[]} dependencies
   * @non-lazy-load
   * @category Data
   */
  /**
   * The optional {@link Scheduler.data.DependencyStore}.
   * @config {Scheduler.data.DependencyStore|Scheduler.model.DependencyStoreConfig} dependencyStore
   * @category Data
   */
  // Properties
  /**
   * Get/set events, applies to the backing project's EventStore.
   * @member {Scheduler.model.EventModel[]} events
   * @accepts {Scheduler.model.EventModel[]|Scheduler.model.EventModelConfig[]}
   * @category Data
   */
  /**
   * Get/set the event store instance of the backing project.
   * @member {Scheduler.data.EventStore} eventStore
   * @category Data
   */
  /**
   * Get/set resources, applies to the backing project's ResourceStore.
   * @member {Scheduler.model.ResourceModel[]} resources
   * @accepts {Scheduler.model.ResourceModel[]|Scheduler.model.ResourceModelConfig[]}
   * @category Data
   */
  /**
   * Get/set the resource store instance of the backing project
   * @member {Scheduler.data.ResourceStore} resourceStore
   * @category Data
   */
  /**
   * Get/set assignments, applies to the backing project's AssignmentStore.
   * @member {Scheduler.model.AssignmentModel[]} assignments
   * @accepts {Scheduler.model.AssignmentModel[]|Object[]}
   * @category Data
   */
  /**
   * Get/set the event store instance of the backing project.
   * @member {Scheduler.data.AssignmentStore} assignmentStore
   * @category Data
   */
  /**
   * Get/set dependencies, applies to the backing projects DependencyStore.
   * @member {Scheduler.model.DependencyModel[]} dependencies
   * @accepts {Scheduler.model.DependencyModel[]|Scheduler.model.DependencyModelConfig[]}
   * @category Data
   */
  /**
   * Get/set the dependencies store instance of the backing project.
   * @member {Scheduler.data.DependencyStore} dependencyStore
   * @category Data
   */
  //endregion
  //region Events
  /**
   * Fired after rendering an event, when its element is available in DOM.
   * @event renderEvent
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.model.EventModel} eventRecord The event record
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource record
   * @param {Scheduler.model.AssignmentModel} assignmentRecord The assignment record
   * @param {Object} renderData An object containing details about the event rendering, see
   *   {@link Scheduler.view.mixin.SchedulerEventRendering#config-eventRenderer} for details
   * @param {Boolean} isRepaint `true` if this render is a repaint of the event, updating its existing element
   * @param {Boolean} isReusingElement `true` if this render lead to the event reusing a released events element
   * @param {HTMLElement} element The event bar element
   */
  /**
   * Fired after releasing an event, useful to cleanup of custom content added on `renderEvent` or in `eventRenderer`.
   * @event releaseEvent
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.model.EventModel} eventRecord The event record
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource record
   * @param {Scheduler.model.AssignmentModel} assignmentRecord The assignment record
   * @param {Object} renderData An object containing details about the event rendering
   * @param {HTMLElement} element The event bar element
   */
  /**
   * Fired when clicking a resource header cell
   * @event resourceHeaderClick
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource record
   * @param {Event} event The event
   */
  /**
   * Fired when double clicking a resource header cell
   * @event resourceHeaderDblclick
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource record
   * @param {Event} event The event
   */
  /**
   * Fired when activating context menu on a resource header cell
   * @event resourceHeaderContextmenu
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource record
   * @param {Event} event The event
   */
  /**
   * Triggered when a keydown event is observed if there are selected events.
   * @event eventKeyDown
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.model.EventModel[]} eventRecords The selected event records
   * @param {Scheduler.model.AssignmentModel[]} assignmentRecords The selected assignment records
   * @param {KeyboardEvent} event Browser event
   */
  /**
   * Triggered when a keyup event is observed if there are selected events.
   * @event eventKeyUp
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.model.EventModel[]} eventRecords The selected event records
   * @param {Scheduler.model.AssignmentModel[]} assignmentRecords The selected assignment records
   * @param {KeyboardEvent} event Browser event
   */
  //endregion
  //region Functions injected by features
  // For documentation & typings purposes
  /**
   * Opens an editor UI to edit the passed event, or a config object for a new event.
   *
   * *NOTE: Only available when the {@link Scheduler/feature/EventEdit} feature is enabled.*
   *
   * @function editEvent
   * @param {Scheduler.model.EventModel|EventModelConfig} eventRecord Event to edit
   * @param {Scheduler.model.ResourceModel} [resourceRecord] The Resource record for the event.
   * This parameter is needed if the event is newly created for a resource and has not been assigned, or when using
   * multi assignment.
   * @param {HTMLElement} [element] Element to anchor editor to (defaults to events element)
   * @category Feature shortcuts
   */
  /**
   * Returns the dependency record for a DOM element
   *
   * *NOTE: Only available when the {@link Scheduler/feature/Dependencies} feature is enabled.*
   *
   * @function resolveDependencyRecord
   * @param {HTMLElement} element The dependency line element
   * @returns {Scheduler.model.DependencyModel} The dependency record
   * @category Feature shortcuts
   */
  //endregion
  //region Init
  afterConstruct() {
    super.afterConstruct();
    this.ion({
      scroll: "onVerticalScroll",
      splitterDragStart: "internalOnSplitterDragStart",
      thisObj: this
    });
  }
  //endregion
  //region Overrides
  onPaintOverride() {
  }
  //endregion
  //region Config getters/setters
  // Placeholder getter/setter for mixins, please make any changes needed to SchedulerStores#store instead
  get store() {
    return super.store;
  }
  set store(store) {
    super.store = store;
  }
  /**
   * Returns an object defining the range of visible resources
   * @property {Object}
   * @property {Scheduler.model.ResourceModel} visibleResources.first First visible resource
   * @property {Scheduler.model.ResourceModel} visibleResources.last Last visible resource
   * @readonly
   * @category Resources
   */
  get visibleResources() {
    var _a5, _b2;
    const me = this;
    if (me.isVertical) {
      return me.currentOrientation.visibleResources;
    }
    return {
      first: me.store.getById((_a5 = me.firstVisibleRow) == null ? void 0 : _a5.id),
      last: me.store.getById((_b2 = me.lastVisibleRow) == null ? void 0 : _b2.id)
    };
  }
  //endregion
  //region Event handlers
  onLocaleChange() {
    this.currentOrientation.onLocaleChange();
    super.onLocaleChange();
  }
  changeCreateEventOnDblClick(createEventOnDblClick) {
    VersionHelper.deprecate("scheduler", "7.0.0", "createEventOnDblClick is deprecated use autoCreate instead");
    this.autoCreate = createEventOnDblClick;
  }
  changeAutoCreate(autoCreate) {
    if (autoCreate) {
      const type = typeof autoCreate;
      if (type !== "object") {
        autoCreate = {
          gesture: type === "string" ? autoCreate : "dblclick"
        };
      }
      autoCreate = Config.merge(autoCreate, _SchedulerBase.$meta.config.autoCreate);
      if (autoCreate.duration) {
        autoCreate.duration = DateHelper.parseDuration(autoCreate.duration);
      }
    }
    return autoCreate;
  }
  updateAutoCreate(autoCreate, was) {
    if ((autoCreate == null ? void 0 : autoCreate.gesture) !== (was == null ? void 0 : was.gesture)) {
      this.detachListeners("scheduleAutoCreateGesture");
      this.ion({
        name: "scheduleAutoCreateGesture",
        [`schedule${autoCreate.gesture}`]: "onScheduleAutoCreateGesture",
        thisObj: this
      });
    }
    if (!was) {
      this.updateLocalization();
    }
  }
  async onScheduleAutoCreateGesture({ domEvent, time, resourceRecord }) {
    const me = this, autoCreate = me.autoCreate || emptyObject14, snapType = autoCreate.snapType || "floor", { step } = autoCreate, date2 = step ? DateHelper[snapType](time, DateHelper.parseDuration(step)) : me.timeAxis[`${snapType}Date`](time);
    if (await me.trigger("beforeAutoCreate", {
      domEvent,
      date: date2,
      resourceRecord
    }) !== false) {
      return me.createEvent(date2, resourceRecord, true);
    }
  }
  onVerticalScroll({ scrollTop }) {
    this.currentOrientation.updateFromVerticalScroll(scrollTop);
  }
  internalOnSplitterDragStart() {
    if (this.isHorizontal && this.foregroundCanvas.syncIdMap) {
      if (Object.keys(this.foregroundCanvas.syncIdMap).length > this.liveRedrawWhileMovingSplitterThreshold) {
        this.suspendRefresh();
        this.ion({ splitterDragEnd: () => this.resumeRefresh(true, false), once: true });
      }
    }
  }
  /**
   * Called when new event is created.
   * Сan be overridden to supply default record values etc.
   * @param {Scheduler.model.EventModel} eventRecord Newly created event
   * @category Scheduled events
   */
  onEventCreated(eventRecord) {
  }
  //endregion
  //region Mode
  /**
   * Checks if scheduler is in horizontal mode
   * @returns {Boolean}
   * @readonly
   * @category Common
   * @private
   */
  get isHorizontal() {
    return this.mode === "horizontal";
  }
  /**
   * Checks if scheduler is in vertical mode
   * @returns {Boolean}
   * @readonly
   * @category Common
   * @private
   */
  get isVertical() {
    return this.mode === "vertical";
  }
  /**
   * Get mode (horizontal/vertical)
   * @property {'horizontal'|'vertical'}
   * @readonly
   * @category Common
   */
  get mode() {
    return this._mode;
  }
  set mode(mode) {
    const me = this;
    me._mode = mode;
    if (!me[mode]) {
      me.element.classList.add(`b-sch-${mode}`);
      if (mode === "horizontal") {
        me.horizontal = new HorizontalRendering(me);
        if (me.isPainted) {
          me.horizontal.init();
        }
      } else if (mode === "vertical") {
        me.vertical = new VerticalRendering(me);
        me.sortFeatureStore = "resourceStore";
        if (me.rendered) {
          me.vertical.init();
        }
      }
    }
  }
  get currentOrientation() {
    return this[this.mode];
  }
  //endregion
  //region Dom event dummies
  // this is ugly, but needed since super cannot be called from SchedulerDomEvents mixin...
  onElementKeyDown(event) {
    return super.onElementKeyDown(event);
  }
  onElementKeyUp(event) {
    return super.onElementKeyUp(event);
  }
  onElementMouseOver(event) {
    return super.onElementMouseOver(event);
  }
  onElementMouseOut(event) {
    return super.onElementMouseOut(event);
  }
  //endregion
  //region Feature hooks
  // Called for each event during drop
  processEventDrop() {
  }
  processCrossSchedulerEventDrop() {
  }
  // Called before event drag starts
  beforeEventDragStart() {
  }
  // Called after event drag starts
  afterEventDragStart() {
  }
  // Called after aborting a drag
  afterEventDragAbortFinalized() {
  }
  // Called during event drag validation
  checkEventDragValidity() {
  }
  // Called after event resizing starts
  afterEventResizeStart() {
  }
  // Called after generating a DomConfig for an event
  afterRenderEvent() {
  }
  //endregion
  //region Scheduler specific date mapping functions
  get hasEventEditor() {
    return Boolean(this.eventEditingFeature);
  }
  get eventEditingFeature() {
    const {
      eventEdit,
      taskEdit,
      simpleEventEdit
    } = this.features;
    return (eventEdit == null ? void 0 : eventEdit.enabled) ? eventEdit : (taskEdit == null ? void 0 : taskEdit.enabled) ? taskEdit : (simpleEventEdit == null ? void 0 : simpleEventEdit.enabled) ? simpleEventEdit : null;
  }
  /**
   * Creates an event on the specified date which conforms to the {@link #config-autoCreate}
   * setting for the specified resource. The event is then scrolled into view.
   *
   * NOTE: If the scheduler is readonly, or resource type is invalid (group header), or if `allowOverlap` is `false`
   * and slot is already occupied - no event is created.
   *
   * This method may be called programmatically by application code if the {@link #config-autoCreate}
   * setting is `false`, in which case the default values for {@link #config-autoCreate} will be used.
   *
   * If the {@link Scheduler.feature.EventEdit} feature is active, the new event
   * will be displayed in the event editor.
   * @param {Date} date The date to add the event at.
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource to create the event for.
   * @category Scheduled events
   */
  async createEvent(startDate, resourceRecord) {
    var _a5, _b2, _c2, _d, _e;
    const me = this, {
      enableEventAnimations,
      eventStore,
      assignmentStore,
      hasEventEditor
    } = me, autoCreate = me.autoCreate || emptyObject14, {
      newName,
      useEventModelDefaults
    } = autoCreate, duration = (autoCreate == null ? void 0 : autoCreate.duration) || {
      magnitude: useEventModelDefaults ? (_a5 = eventStore.modelClass.defaultValues.duration) != null ? _a5 : 1 : 1,
      unit: useEventModelDefaults ? (_b2 = eventStore.modelClass.defaultValues.durationUnit) != null ? _b2 : me.timeAxis.unit : me.timeAxis.unit
    }, resourceRecords = [resourceRecord], eventRecord = eventStore.createRecord({
      startDate,
      endDate: DateHelper.add(startDate, duration),
      duration: duration.magnitude,
      durationUnit: duration.unit,
      name: me.resolveCallback(newName, me, false) ? me.callback(newName, me, [me, startDate, resourceRecord]) : newName
    });
    if (me.readOnly || resourceRecord.isSpecialRow || resourceRecord.readOnly || !me.isDateRangeAvailable(
      eventRecord.startDate,
      eventRecord.endDate,
      null,
      resourceRecord
    )) {
      return;
    }
    (_c2 = me.eventEditingFeature) == null ? void 0 : _c2.captureStm(true);
    eventRecord.isCreating = hasEventEditor;
    me.onEventCreated(eventRecord);
    const assignmentRecords = assignmentStore == null ? void 0 : assignmentStore.assignEventToResource(eventRecord, resourceRecord);
    if (me.trigger("beforeEventAdd", { eventRecord, resourceRecords, assignmentRecords }) === false) {
      assignmentStore == null ? void 0 : assignmentStore.remove(assignmentRecords);
      (_d = me.eventEditingFeature) == null ? void 0 : _d.freeStm(false);
      return;
    }
    me.enableEventAnimations = false;
    const result = eventStore.add(eventRecord);
    me.project.commitAsync().then(() => me.enableEventAnimations = enableEventAnimations);
    if (!result) {
      assignmentStore == null ? void 0 : assignmentStore.remove(assignmentRecords);
      (_e = me.eventEditingFeature) == null ? void 0 : _e.freeStm(false);
      return;
    }
    me.isCreating = true;
    me.refreshRows();
    me.isCreating = false;
    await me.scrollEventIntoView(eventRecord);
    if (me.isDestroyed) {
      return;
    }
    me.trigger("eventAutoCreated", {
      eventRecord,
      resourceRecord
    });
    if (hasEventEditor) {
      me.editEvent(eventRecord, resourceRecord, me.getEventElement(eventRecord));
    }
  }
  /**
   * Checks if a date range is allocated or not for a given resource.
   * @param {Date} startDate The start date
   * @param {Date} endDate The end date
   * @param {Scheduler.model.EventModel|null} excludeEvent An event to exclude from the check (or null)
   * @param {Scheduler.model.ResourceModel|Scheduler.model.ResourceModel[]} resourceRecord A resource or an array of resource records
   * @returns {Boolean} True if the timespan is available for the resource(s)
   * @category Dates
   */
  isDateRangeAvailable(startDate, endDate, excludeEvent, resourceRecord) {
    return this.eventStore.isDateRangeAvailable({ startDate, endDate, excludeEvent, resourceRecord, allowOverlap: this.allowOverlap });
  }
  //endregion
  /**
   * Resumes UI refresh on store operations.
   *
   * Multiple calls to `suspendRefresh` stack up, and will require an equal number of `resumeRefresh` calls to
   * actually resume UI refresh.
   *
   * Specify `true` as the first param to trigger a refresh if this call unblocked the refresh suspension.
   * If the underlying project is calculating changes, the refresh will be postponed until it is done.
   *
   * @param {Boolean} [trigger=true] Pass `false` to not trigger a refresh, if this resume unblocks suspension
   * @privateparam {Boolean} [useTransitions] `false` to block transitions
   * @category Rendering
   */
  async resumeRefresh(trigger = true, useTransitions = true) {
    super.resumeRefresh(false);
    const me = this;
    if (!me.refreshSuspended && trigger) {
      if (!me.isEngineReady) {
        me.currentOrientation.refreshAllWhenReady = true;
        return me.project.commitAsync();
      }
      if (!me.isDestroyed) {
        if (useTransitions) {
          me.refreshWithTransition();
        } else {
          me.refresh();
        }
      }
    }
  }
  /**
   * Triggers a render of all the cells (in horizontal mode) in and event bars for the passed resource.
   *
   * Since Scheduler uses virtualized resources, calling this method for a resource that is not currently rendered
   * will not have any effect.
   *
   * {@note}Manipulating resource or event records and Scheduler settings refreshes the Scheduler automatically. You
   * should only need to call this function if you have outside data/settings unknown to the Scheduler that has
   * changed and requires the resource to update to reflect the changes{/@note}
   *
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource that should be refreshed
   * @category Rendering
   */
  refreshResource(resourceRecord) {
    this.refreshResources([resourceRecord]);
  }
  /**
   * Triggers a render of all the cells (in horizontal mode) and event bars for the passed resources. Leave the
   * argument out to refresh all resources.
   *
   * Since Scheduler uses virtualized resources, calling this method for resources that are not currently rendered
   * will not have any effect.
   *
   * {@note}Manipulating resource or event records and Scheduler settings refreshes the Scheduler automatically. You
   * should only need to call this function if you have outside data/settings unknown to the Scheduler that has
   * changed and requires some resources to update to reflect the changes{/@note}
   *
   * @param {Scheduler.model.ResourceModel[]} resourceRecords The resources that should be refreshed
   * @category Rendering
   */
  refreshResources(resourceRecords) {
    this.currentOrientation.refreshResources(resourceRecords);
  }
  //region Appearance
  // Overrides grid to take crudManager loading into account
  toggleEmptyText() {
    var _a5;
    DomHelper.toggleClasses(this.element, "b-grid-empty", !(this.resourceStore.count > 0 || ((_a5 = this.crudManager) == null ? void 0 : _a5.isLoading)));
  }
  // Overrides Grids base implementation to return a correctly calculated height for the row. Also stores it in
  // RowManagers height map, which is used to calculate total height etc.
  getRowHeight(resourceRecord) {
    if (this.isHorizontal) {
      const height = this.currentOrientation.calculateRowHeight(resourceRecord);
      this.rowManager.storeKnownHeight(resourceRecord.id, height);
      return height;
    }
  }
  // Calculates the height for specified rows. Call when changes potentially makes its height invalid
  calculateRowHeights(resourceRecords, silent = false) {
    const { store } = this;
    for (const resourceRecord of resourceRecords) {
      if (resourceRecord && store.isAvailable(resourceRecord)) {
        this.getRowHeight(resourceRecord);
      }
    }
    if (!silent) {
      this.rowManager.estimateTotalHeight(true);
    }
  }
  // Calculate heights for all rows (up to the preCalculateHeightLimit)
  calculateAllRowHeights(silent = false) {
    const { store, rowManager } = this, count = Math.min(store.count, this.preCalculateHeightLimit);
    if (count) {
      rowManager.clearKnownHeights();
      for (let i = 0; i < count; i++) {
        const record = store.records[i];
        if (!record) {
          continue;
        }
        this.getRowHeight(record);
      }
      if (!silent) {
        rowManager.estimateTotalHeight(true);
      }
    }
  }
  //endregion
  //region Calendar Mode Interface
  // These are all internal and match up w/CalendarMixin
  /**
   * Returns the date or ranges of included dates as an array. If only the {@link #config-startDate} is significant,
   * the array will have that date as its only element. Otherwise, a range of dates is returned as a two-element
   * array with `[0]` is the {@link #config-startDate} and `[1]` is the {@link #property-lastDate}.
   * @member {Date[]}
   * @internal
   */
  get dateBounds() {
    const me = this, ret = [me.startDate];
    if (me.range.unit === "week") {
      ret.push(me.lastDate);
    }
    return ret;
  }
  get description() {
    const me = this, { startDate, endDate } = me, hasRenderer = me.resolveCallback("descriptionRenderer", me, false);
    if (!hasRenderer && me.range.unit === "day" && endDate - startDate && !DateHelper.isStartOf(startDate, "d") && !DateHelper.isStartOf(endDate, "d")) {
      const startMonth = DateHelper.getMonthName(startDate.getMonth()), endMonth = DateHelper.getMonthName(endDate.getMonth()), endMonthName = startMonth !== endMonth ? `${endMonth} ` : "";
      return `${startMonth} ${startDate.getDate()} - ${endMonthName}${endDate.getDate()}, ${endDate.getFullYear()}`;
    }
    return super.description;
  }
  get defaultDescriptionFormat() {
    return descriptionFormats[this.range.unit];
  }
  /**
   * The last day that is included in the date range. This is different than {@link #config-endDate} since that date
   * is not inclusive. For example, an `endDate` of 2022-07-21 00:00:00 indicates that the time range ends at that
   * time, and so 2022-07-21 is _not_ in the range. In this example, `lastDate` would be 2022-07-20 since that is the
   * last day included in the range.
   * @member {Date}
   * @internal
   */
  get lastDate() {
    const { endDate } = this;
    if (endDate) {
      return DateHelper.isStartOf(endDate, "d") ? DateHelper.add(endDate, -1, "day") : endDate;
    }
  }
  getEventRecord(target) {
    target = DomHelper.getEventElement(target);
    return this.resolveEventRecord(target);
  }
  getResourceRecord(domEvent) {
    return this.resolveResourceRecord(domEvent);
  }
  getEventElement(eventRecord) {
    return this.getElementFromEventRecord(eventRecord);
  }
  /**
   * {@note}This property is for use when the Scheduler is used as a Calendar view - a `mode`.{/@note}
   *
   * This property yields a {@link Core.widget.Menu} configuration block which encapsulates the range choices
   * which this widget may be set to encapsulate:
   * - day
   * - week
   * - month
   * - year
   * - decade
   *
   * By default a `list` view adds these choices to the header context menu.
   * An `agenda` view creates a floating settings button which offers this menu.
   * The property may be used to create a custom UI for changing the range.
   * The value yielded by the default `get listRangeMenu()` implementation looks like this:
   *
   * ```javascript
   * {
   *     items : {
   *         listRangeDayItem    : {config for DAY range menu item },
   *         listRangWeekItem    : {config for WEEK range menu item },
   *         listRangMonthItem   : {config for MONTH range menu item },
   *         listRangeYearItem   : {config for YEAR range menu item },
   *         listRangeDecadeItem : {config for DECADE range menu item }
   *     }
   * }
   * ```
   *
   * A subclass can override `get listRangeMenu()` and mutate the object returned by the `super` call.
   *
   * For example, it could `delete` properties of the `items` block to limit which ranges may be selected.
   *
   * @member {MenuConfig} listRangeMenu
   * @readonly
   */
  changeListRangeMenu(listRangeMenu) {
    if (listRangeMenu) {
      return TimelineBase.mergeConfigs({
        view: this
      }, listRangeMenu);
    }
  }
  changeRange(range) {
    if (range) {
      if (typeof range === "string") {
        const unit = DateHelper.parseTimeUnit(range);
        if (unit) {
          range = {
            magnitude: 1,
            unit
          };
        } else {
          range = DateHelper.parseDuration(range);
        }
      } else if (typeof range === "number") {
        return {
          magnitude: range,
          unit: "day"
        };
      }
    }
    return range;
  }
  updateRange(range) {
    if (!this.isConfiguring) {
      const currentDate = this.date, newDate = this.date = DateHelper.clamp(currentDate, DateHelper.floor(currentDate, range), DateHelper.ceil(currentDate, range));
      if (currentDate.getTime() === newDate.getTime()) {
        this.updateDate(newDate, currentDate, true);
      }
    }
  }
  changeShiftIncrement(shiftIncrement) {
    return this.changeRange(shiftIncrement);
  }
  updateShiftIncrement(old, shiftIncrement) {
    this.trigger("shiftIncrementChange", {
      old,
      new: shiftIncrement
    });
  }
  get shiftIncrement() {
    return this._shiftIncrement || this.range;
  }
  get stepUnit() {
    const { shiftIncrement } = this;
    return shiftIncrement.magnitude === 1 ? DateHelper.getLocalizedNameOfUnit(shiftIncrement.unit) : `${shiftIncrement.magnitude} ${DateHelper.getLocalizedNameOfUnit(shiftIncrement.unit, true)}`;
  }
  set stepUnit(stepUnit) {
    VersionHelper.deprecate("scheduler", "7.0.0", "changeStepUnit is deprectaed use shiftIncrement instead");
    this.shiftIncrement = stepUnit;
  }
  // When scrolling to a date, it implies bringing the entire "day cell" into view.
  async updateDate(newDate, was, changeTimeSpan) {
    var _a5;
    const me = this, {
      timeAxis,
      vertical,
      range,
      workingTime
    } = me, visibleStartDate = DateHelper.clearTime(((_a5 = me.visibleDateRange) == null ? void 0 : _a5.startDate) || newDate), visibleDate = changeTimeSpan ? visibleStartDate : newDate, { startDate: oldStartDate } = timeAxis, snappedStartDate = DateHelper.floor(oldStartDate, range), snappedEndDate = DateHelper.add(snappedStartDate, range), increment = me.isShifting ? me.shiftIncrement : me.range, startDate = DateHelper.floor(oldStartDate, increment, oldStartDate), endDate = DateHelper.add(startDate, range), scrollOpts = {
      [vertical ? "x" : "y"]: false,
      animate: true,
      block: newDate < startDate ? "start" : newDate >= endDate ? "end" : "nearest"
    }, oldWidth = me.timeAxisColumn.width;
    if (changeTimeSpan || !DateHelper.betweenLesser(newDate, snappedStartDate, snappedEndDate)) {
      const newStartDate = DateHelper.floor(newDate, range), newEndDate = DateHelper.add(newStartDate, range);
      if (workingTime) {
        newStartDate.setHours(workingTime.fromHour);
        newEndDate.setHours(workingTime.toHour - 24);
      }
      Object.assign(scrollOpts, {
        animate: false,
        block: "start",
        edgeOffset: changeTimeSpan ? { start: me.getCoordinateFromDate(visibleStartDate) - me.timelineScroller.position } : 0
      });
      me.setTimeSpan(newStartDate, newEndDate);
      if (me.timeAxisColumn.width < oldWidth) {
        await AsyncHelper.sleep(0);
      }
    }
    me._date = DateHelper.clamp(newDate, timeAxis.startDate, timeAxis.endDate);
    if (!me.preventScroll) {
      if (me.isConfiguring) {
        me.visibleDate = {
          date: me._date,
          block: "start",
          animate: me.isPainted
        };
      } else {
        const dateStart = me.getCoordinateFromDate(visibleDate, false), dateEnd = me.getCoordinateFromDate(DateHelper.min(DateHelper.add(visibleDate, 1, "d"), timeAxis.endDate), false), dayWidth = Math.abs((dateEnd === -1 ? me.timelineScroller.scrollSize : dateEnd) - dateStart), dayCell = vertical ? new Rectangle(0, dateStart, 1, dayWidth) : new Rectangle(me.rtl ? dateEnd : dateStart, 0, Math.abs(dayWidth), 1);
        me.timelineScroller.scrollIntoView(dayCell, scrollOpts);
      }
    }
    me.trigger("descriptionChange");
  }
  updateScrollBuffer(value2) {
    if (!this.isConfiguring) {
      this.currentOrientation.scrollBuffer = value2;
    }
  }
  /**
   * Interface method used by an encapsulating Calendar view to implement the "prev" button.
   * @category Calendar integration
   */
  previous() {
    this.move(-1);
  }
  /**
   * Interface method used by an encapsulating Calendar view to implement the "next" button.
   * @category Calendar integration
   */
  next() {
    this.move(1);
  }
  move(direction = 1) {
    direction = Math.sign(direction);
    const me = this, {
      shiftIncrement
    } = me;
    if (shiftIncrement) {
      me.isShifting = true;
      me.date = DateHelper.add(me.date, { unit: shiftIncrement.unit, magnitude: direction * shiftIncrement.magnitude });
      me.isShifting = false;
    }
  }
  //endregion
  /**
   * Assigns and schedules an unassigned event record (+ adds it to this Scheduler's event store unless already in it).
   * @param {Object} config The config containing data about the event record to schedule
   * @param {Date} config.startDate The start date
   * @param {Scheduler.model.EventModel|EventModelConfig} config.eventRecord Event (or data for it) to assign and schedule
   * @param {Scheduler.model.EventModel} [config.parentEventRecord] Parent event to add the event to (to nest it),
   * only applies when using the NestedEvents feature
   * @param {Scheduler.model.ResourceModel} config.resourceRecord Resource to assign the event to
   * @param {HTMLElement} [config.element] The element if you are dragging an element from outside the scheduler
   * @category Scheduled events
   */
  async scheduleEvent({ startDate, eventRecord, resourceRecord, element }) {
    const me = this;
    if (!me.eventStore.includes(eventRecord)) {
      [eventRecord] = me.eventStore.add(eventRecord);
    }
    eventRecord.beginBatch();
    eventRecord.startDate = startDate;
    eventRecord.assign(resourceRecord);
    eventRecord.endBatch();
    if (element) {
      const eventRect = Rectangle.from(element, me.foregroundCanvas);
      DomHelper.setTranslateXY(element, 0, 0);
      DomHelper.setTopLeft(element, eventRect.y, eventRect.x);
      DomSync.addChild(me.foregroundCanvas, element, eventRecord.assignments[0].id);
    }
    await me.project.commitAsync();
    return eventRecord;
  }
  /**
   * Internal function to get an array of assignment instances from:
   * * Single event or assignment instance
   * * Array of event or assignment instances
   * * Single event or assignment id (depending on `usesSingleAssignment`)
   * * Array of event or assignment ids (depending on `usesSingleAssignment`)
   * @internal
   */
  internalGetAssignmentsToHighlight(eventsOrAssignments) {
    const { eventStore, assignmentStore, resourceStore } = this, { usesSingleAssignment } = eventStore;
    return ArrayHelper.asArray(eventsOrAssignments).reduce((acc, eventOrAssignment) => {
      const store = usesSingleAssignment && !eventOrAssignment.isModel || eventOrAssignment.isEvent ? eventStore : assignmentStore;
      eventOrAssignment = store.getById(eventOrAssignment);
      if (eventOrAssignment == null ? void 0 : eventOrAssignment.isEvent) {
        acc.push(...eventOrAssignment.assignments.filter((a) => a.resource.stores.includes(resourceStore)));
      } else if ((eventOrAssignment == null ? void 0 : eventOrAssignment.isAssignment) && eventOrAssignment.resource.stores.includes(resourceStore)) {
        acc.push(eventOrAssignment);
      }
      return acc;
    }, []);
  }
};
//region Config
__publicField(_SchedulerBase, "$name", "SchedulerBase");
// Factoryable type name
__publicField(_SchedulerBase, "type", "schedulerbase");
__publicField(_SchedulerBase, "configurable", {
  /**
   * Get/set the scheduler's read-only state. When set to `true`, any UIs for modifying data are disabled.
   * @member {Boolean} readOnly
   * @category Misc
   */
  /**
   * Configure as `true` to make the scheduler read-only, by disabling any UIs for modifying data.
   *
   * __Note that checks MUST always also be applied at the server side.__
   * @config {Boolean} readOnly
   * @default false
   * @category Misc
   */
  localizableProperties: ["autoCreate.newName"],
  /**
   * The date to display when used as a component of a Calendar.
   *
   * This is required by the Calendar Mode Interface.
   *
   * @prp {Date}
   * @category Calendar integration
   */
  date: {
    value: null,
    $config: {
      equal: "date"
    }
  },
  /**
   * {@note}This property is for use when the Scheduler is used as a Calendar view - a `mode`.{/@note}
   *
   * A {@link Core.widget.Menu} configuration block which configures the range choosing menu provided which
   * by default selects one of the following:
   * - day
   * - week
   * - month
   * - year
   * - decade
   *
   * This menu is added to the {@link Scheduler.feature.TimeAxisHeaderMenu} when the Scheduler is used
   * as a Calendar view.
   *
   * This may be used to either reconfigure that menu, or, by configuring it as `null`, could remove
   * the menu entirely if the date range of this view is controlled by other means.
   *
   * The individual range menu items are under the `items` property and have the following
   * property names:
   * - `listRangeDayItem`
   * - `listRangeWeekItem`
   * - `listRangeMonthItem`
   * - `listRangeYearItem`
   * - `listRangeDecadeItem`
   *
   * These may be reconfigured:
   *
   * ```javascript
   * calendar = new Calendar({
   *     ...
   *     modes : {
   *         timeline : {
   *             type          : 'scheduler',
   *             range         : 'month',
   *             listRangeMenu : {
   *                 items : {
   *                     // We don't want the decade range option
   *                     listRangeDecadeItem : null
   *                 }
   *             }
   *         }
   *     }
   * });
   * ```
   *
   * @config {MenuConfig} listRangeMenu
   */
  listRangeMenu: {
    $config: "lazy",
    value: {
      type: "rangemenu"
    }
  },
  /**
   * The time unit to move by when {@link #function-next} or {@link #function-previous} is called.
   *
   * {@note}Setting of this is deprecated, please use {@link #config-shiftIncrement} instead.{/@note}
   * @config {DurationUnit} [stepUnit=null]
   * @category Calendar integration
   * @deprecated 6.1.6 Use {@link #config-shiftIncrement} instead.
   */
  /**
   * {@note}This property is for use when the Scheduler is used as a Calendar view - a `mode`.
   * it will have no effect on a Scheduler which is not inside a Calendar.{/@note}
   * The time range to move by when {@link #function-next} or {@link #function-previous} is called.
   *
   * If not specified, this view moves by its duration in days as derived from its {@link #property-range}.
   *
   * Valid values are:
   * - day
   * - week
   * - month
   * - year
   * - decade
   *
   * This may also be specified as a duration with a magnitude part and a unit part. For
   * example `'1m'` would mean one month, and `'4w'` would mean four weeks.
   * See {@link Core.helper.DateHelper#function-parseDuration-static} for details
   * of syntax.
   *
   * When using a range of weeks, months, years or decades, then when this widget's
   * {@link #config-date} is synced with its owning Calendar's date, this widget's
   * {@link #config-startDate} is snapped to the closest start point of the range
   * which encompasses that date.
   *
   * So if using `range : '1w'`, then setting the date to Thursday, 28th October 2021
   * Would mean that the `startDate` snaps to Sunday 24th October 2021 (assuming the locale
   * uses Sunday as the week start day).
   *
   * If configured to use a range of *days*, no snapping is done. There's no defined start point
   * so the {@link #config-startDate} is set to the incoming Calendar date.
   * @prp {DurationConfig}
   * @category Calendar integration
   * @accepts {String|DurationConfig}
   */
  shiftIncrement: {
    $config: {
      lazy: true,
      equal: compareRange
    },
    value: null
  },
  /**
   * The time range encapsulated by the current {@link #property-date}.
   *
   * When a range is used, changing the {@link #config-date} snaps the {@link #config-startDate}
   * to the closest starting date of the range. For Example if the range was configured as `'1 week'`
   * then setting the date to the date of next Wednesday would mean that the {@link #property-startDate}
   * would be the __start__ of next week, and an entire week would be encapsulated by this view.
   * @member {DurationConfig} range
   * @accepts {String|DurationConfig}
   */
  /**
   * Range used to set the length of the time axis when used as a component of a Calendar. Suitable units are
   * `'month'`, `'week'` and `'day'`.
   *
   * This may also be specified as a duration with a magnitude part and a unit part. For
   * example `'1m'` would mean one month, and `'4w'` would mean four weeks.
   * See {@link Core.helper.DateHelper#function-parseDuration-static} for details
   * of syntax.
   *
   * When using a range of weeks or months, then when this widget's {@link #config-date} is synced
   * with its owning Calendar's date property, this widget's {@link #config-startDate}
   * is snapped to the closest start point of the range which encompasses that date.
   *
   * So if using `range : '1w'`, then setting the date to Thursday, 28th October 2021
   * Would mean that the `startDate` snaps to Sunday 24th October 2021 (assuming the locale
   * uses Sunday as the week start day).
   *
   * @config {String|DurationConfig}
   * @category Calendar integration
   * @default
   */
  range: {
    $config: {
      equal: compareRange
    },
    value: "week"
  },
  /**
   * When the scheduler is used in a Calendar, this function provides the textual description for the
   * Calendar's toolbar.
   *
   * ```javascript
   *  descriptionRenderer : scheduler => {
   *      const
   *          count = scheduler.eventStore.records.filter(
   *              eventRec => DateHelper.intersectSpans(
   *                  scheduler.startDate, scheduler.endDate,
   *                  eventRec.startDate, eventRec.endDate)).length,
   *          startDate = DateHelper.format(scheduler.startDate, 'DD/MM/YYY'),
   *          endData = DateHelper.format(scheduler.endDate, 'DD/MM/YYY');
   *
   *      return `${startDate} - ${endData}, ${count} event${count === 1 ? '' : 's'}`;
   *  }
   * ```
   * @config {Function}
   * @param {Scheduler.view.SchedulerBase} view The active view.
   * @returns {String}
   * @category Calendar integration
   */
  /**
   * A method allowing you to define date boundaries that will constrain resize, create and drag drop
   * operations. The method will be called with the Resource record, and the Event record.
   *
   * ```javascript
   *  new Scheduler({
   *      getDateConstraints(resourceRecord, eventRecord) {
   *          // Assuming you have added these extra fields to your own EventModel subclass
   *          const { minStartDate, maxEndDate } = eventRecord;
   *
   *          return {
   *              start : minStartDate,
   *              end   : maxEndDate
   *          };
   *      }
   *  });
   * ```
   *
   * @config {Function}
   * @param {Scheduler.model.ResourceModel} [resourceRecord] The resource record
   * @param {Scheduler.model.EventModel} [eventRecord] The event record
   * @returns {DateConstraint} Constraining `Object` containing `start` and `end` constraints. Omitting either
   *   will mean that end is not constrained. So you can prevent a resize or move from moving *before*
   *   a certain time while not constraining the end date.
   * @category Scheduled events
   */
  getDateConstraints: null,
  /**
   * The time axis column config for vertical {@link Scheduler.view.SchedulerBase#config-mode}.
   *
   * Object with {@link Scheduler.column.VerticalTimeAxisColumn} configuration.
   *
   * This object will be used to configure the vertical time axis column instance.
   *
   * The config allows configuring the `VerticalTimeAxisColumn` instance used in vertical mode with any Column options that apply to it.
   *
   * Example:
   *
   * ```javascript
   * new Scheduler({
   *     mode     : 'vertical',
   *     features : {
   *         filterBar : true
   *     },
   *     verticalTimeAxisColumn : {
   *         text  : 'Filter by event name',
   *         width : 180,
   *         filterable : {
   *             // add a filter field to the vertical column access header
   *             filterField : {
   *                 type        : 'text',
   *                 placeholder : 'Type to search',
   *                 onChange    : ({ value }) => {
   *                     // filter event by name converting to lowerCase to be equal comparison
   *                     scheduler.eventStore.filter({
   *                         filters : event => event.name.toLowerCase().includes(value.toLowerCase()),
   *                         replace : true
   *                     });
   *                 }
   *             }
   *         }
   *     },
   *     ...
   * });
   * ```
   *
   * @config {VerticalTimeAxisColumnConfig}
   * @category Time axis
   */
  verticalTimeAxisColumn: {},
  /**
   * See {@link Scheduler.view.Scheduler#keyboard-shortcuts Keyboard shortcuts} for details
   * @config {Object<String,KeyMapConfig>} keyMap
   * @category Common
   */
  /**
   * {@note}This is deprecated in favor of {@link #config-autoCreate} which allows for more
   * granular control of how a click or double click creates an event.{/@note}
   * If true, a new event will be created when user double-clicks on a time axis cell (if scheduler is not in
   * read only mode).
   *
   * The duration / durationUnit of the new event will be 1 time axis tick (default), or it can be read from
   * the {@link Scheduler.model.EventModel#field-duration} and
   * {@link Scheduler.model.EventModel#field-durationUnit} fields.
   *
   * Set to `false` to not create events on double click.
   * @config {Boolean|Object} createEventOnDblClick
   * @param {Boolean} [createEventOnDblClick.useEventModelDefaults] set to `true` to set default duration
   * based on the defaults specified by the {@link Scheduler.model.EventModel#field-duration} and
   * {@link Scheduler.model.EventModel#field-durationUnit} fields.
   * @default
   * @category Scheduled events
   * @deprecated 6.1.8 Use {@link #config-autoCreate} instead.
   */
  createEventOnDblClick: null,
  /**
   * If this config is set, then its `gesture` property (which defaults to `dblclick`) creates a
   * new event at the mouse or touch event's time point.
   *
   * When set to `true`, the gesture is `dblclick`, and the new event will have a duration of 1 time axis tick.
   *
   * This may be specified as the string DOM event name to listen for, such as `'click'`, `'dblclick'`
   * and the new event will have a duration of 1 time axis tick.
   *
   * The duration of the new event can be set by specifying the `duration` property as a {@link DurationConfig}.
   *
   * If that is not set, then setting the `useEventModelDefaults` property will cause the duration
   * to be specified by the default values of the {@link Scheduler.model.EventModel#field-duration} and
   * {@link Scheduler.model.EventModel#field-durationUnit} fields
   *
   * Example:
   * ```javascript
   * new Scheduler({
   *     autoCreate : {
   *         gesture  : 'click',
   *         duration : '2 hours',
   *         newName  : 'New appointment'
   *     }
   * });
   * ```
   *
   * @prp {Boolean|String|Object} autoCreate
   * @param {'click'|'dblclick'} [autoCreate.gesture] The gesture to trigger auto creation of an event. Defaults to `dblclick`.
   * @param {Boolean} [autoCreate.useEventModelDefaults] Set to `true` to set default duration
   * based on the defaults specified by the {@link Scheduler.model.EventModel#field-duration} and
   * {@link Scheduler.model.EventModel#field-durationUnit} fields.
   * @param {'round'|'ceil'|'floor'} [autoCreate.snapType] The type of snapping to apply to the event start date.
   * If omitted, the date will be floored to the time axis resolution.
   * @param {DurationConfig|String} [autoCreate.step] The increment to snap the event start date to.
   * @param {DurationConfig|String} [autoCreate.duration] The duration of the event to create. Defaults to 1 time axis tick.
   * @param {String} [autoCreate.name] The name of the event to create. Defaults to the `Object.newEvent` property
   * from the locale.
   * @category Scheduled events
   * @default
   */
  autoCreate: {
    gesture: "dblclick",
    newName: "L{Object.newEvent}"
  },
  /**
   * Number of pixels to horizontally extend the visible render zone by, controlling the events that will be
   * rendered. You can use this to increase or reduce the amount of event rendering happening when scrolling
   * along a horizontal time axis. This can be useful if you render huge amount of events.
   *
   * To force the scheduler to render all events within the TimeAxis start & end dates, set this to -1.
   * The initial render will take slightly longer but no extra event rendering will take place when scrolling.
   *
   * NOTE: This is an experimental API which might change in future releases.
   * @config {Number}
   * @default
   * @internal
   * @category Experimental
   */
  scrollBuffer: 0,
  // A CSS class identifying areas where events can be scheduled using drag-create, double click etc.
  schedulableAreaSelector: ".b-sch-timeaxis-cell",
  scheduledEventName: "event",
  /**
   * If set to `true` this will show a color field in the {@link Scheduler.feature.EventEdit} editor and also a
   * picker in the {@link Scheduler.feature.EventMenu}. Both enables the user to choose a color which will be
   * applied to the event bar's background. See EventModel's
   * {@link Scheduler.model.mixin.EventModelMixin#field-eventColor} config.
   * config.
   * @config {Boolean}
   * @default false
   * @category Misc
   */
  showEventColorPickers: null,
  /**
   * By default, scrolling the schedule will update the {@link #property-timelineContext} to reflect the new
   * currently hovered context. When displaying a large number of events on screen at the same time, this will
   * have a slight impact on scrolling performance. In such scenarios, opt out of this behavior by setting
   * this config to `false`.
   * @default
   * @prp {Boolean}
   * @category Misc
   */
  updateTimelineContextOnScroll: true,
  /**
   * By default, when the EventStore (and similar stores) is lazy loading, a loading indicator will be displayed just
   * below the timeline headers. Set this to `false` to prevent this indicator being shown.
   * @prp {Boolean}
   * @category Masking
   */
  lazyLoadingIndicator: true
});
var SchedulerBase = _SchedulerBase;
SchedulerBase.initClass();
SchedulerBase._$name = "SchedulerBase";

// ../Scheduler/lib/Scheduler/widget/EventColorPicker.js
var EventColorPicker = class extends ColorPicker {
  updateRecord(value2) {
    if (value2) {
      VersionHelper.deprecate("scheduler", "7.0.0", "record config is deprecated in favor of records");
      this.records = [value2];
    }
  }
  colorSelected({ color }) {
    if (this.records) {
      this.records.forEach((record) => record.eventColor = color);
    }
  }
};
__publicField(EventColorPicker, "$name", "EventColorPicker");
__publicField(EventColorPicker, "type", "eventcolorpicker");
__publicField(EventColorPicker, "configurable", {
  colorClasses: SchedulerBase.eventColors,
  colorClassPrefix: "b-sch-",
  /**
   * @hideconfigs colors
   */
  colors: null,
  /**
   * Provide a {@link Scheduler.model.EventModel} instance to update its
   * {@link Scheduler.model.mixin.EventModelMixin#field-eventColor} field
   * @deprecated Use {@link #config-records} instead.
   * @config {Scheduler.model.EventModel}
   */
  record: null,
  /**
   * Provide an array of {@link Scheduler.model.EventModel} instances to update their
   * {@link Scheduler.model.mixin.EventModelMixin#field-eventColor} field
   * @config {Scheduler.model.EventModel[]}
   */
  records: null
});
EventColorPicker.initClass();
EventColorPicker._$name = "EventColorPicker";

// ../Scheduler/lib/Scheduler/widget/EventColorField.js
var EventColorField = class extends ColorField {
};
__publicField(EventColorField, "$name", "EventColorField");
__publicField(EventColorField, "type", "eventcolorfield");
__publicField(EventColorField, "configurable", {
  picker: {
    type: "eventcolorpicker"
  },
  name: "eventColor"
});
EventColorField.initClass();
EventColorField._$name = "EventColorField";

// ../Scheduler/lib/Scheduler/column/EventColorColumn.js
var EventColorColumn = class extends ColorColumn {
};
__publicField(EventColorColumn, "$name", "EventColorColumn");
__publicField(EventColorColumn, "type", "eventcolor");
__publicField(EventColorColumn, "defaults", {
  editor: "eventcolorfield",
  field: "eventColor"
});
ColumnStore.registerColumnType(EventColorColumn);
EventColorColumn._$name = "EventColorColumn";

// ../Scheduler/lib/Scheduler/column/ResourceCollapseColumn.js
var ResourceCollapseColumn = class extends Column {
  static get defaults() {
    return {
      /**
       * @hideconfigs renderer
       */
      width: "3em",
      align: "center",
      sortable: false,
      groupable: false,
      editor: false,
      minWidth: 0,
      cellCls: "b-resourcecollapse-cell",
      renderer: ({ record, column }) => ({
        tag: "button",
        "aria-label": column.L("L{ResourceCollapseColumn.toggleResourceLayout}"),
        class: {
          "b-icon": 1,
          "b-icon-expand-resource": 1,
          "b-flip": record.eventLayout !== "none"
        }
      })
    };
  }
  onCellClick({ record, event }) {
    if (!record.isSpecialRow && event.target.classList.contains("b-icon")) {
      event.preventDefault();
      record.eventLayout = record.eventLayout !== "none" ? "none" : "stack";
    }
  }
};
__publicField(ResourceCollapseColumn, "$name", "ResourceCollapseColumn");
__publicField(ResourceCollapseColumn, "type", "resourceCollapse");
__publicField(ResourceCollapseColumn, "fields", [
  { name: "ariaLabel", defaultValue: "L{ResourceCollapseColumn.toggleResourceLayout}" }
]);
ColumnStore.registerColumnType(ResourceCollapseColumn);
ResourceCollapseColumn._$name = "ResourceCollapseColumn";

// ../Scheduler/lib/Scheduler/column/ResourceInfoColumn.js
var ResourceInfoColumn = class extends Column {
  static get fields() {
    return ["showEventCount", "showRole", "showMeta", "showImage", "autoScaleThreshold", "useNameAsImageName"];
  }
  static get defaults() {
    return {
      /**
       * @hideconfigs renderer
       */
      /**
       * Show image. Looks for image name in fields on the resource in the following order: 'imageUrl', 'image',
       * 'name'. Set `showImage` to a field name to use a custom field. Set `Scheduler.resourceImagePath` to
       * specify where to load images from. If no extension found, defaults to
       * {@link Scheduler.view.mixin.SchedulerEventRendering#config-resourceImageExtension}.
       * @config {Boolean}
       * @default
       */
      showImage: true,
      /**
       * Show number of events assigned to the resource below the name.
       * @config {Boolean}
       * @default
       */
      showEventCount: true,
      /**
       * A template string to render any extra information about the resource below the name
       * @config {Function}
       * @param {Scheduler.model.ResourceModel} resourceRecord The record representing the current row
       * @returns {String|null}
       */
      showMeta: null,
      /**
       * Show resource role below the name. Specify `true` to display data from the `role` field, or specify a field
       * name to read this value from.
       * @config {Boolean|String}
       * @default
       */
      showRole: false,
      /**
       * Specify 0 to prevent the column from adapting its content according to the used row height, or specify a
       * threshold (row height) at which scaling should start.
       * @config {Number}
       * @default
       */
      autoScaleThreshold: 40,
      /**
       * Use the resource name as the image name when no `image` is specified on the resource.
       * @config {Boolean}
       * @default
       */
      useNameAsImageName: true,
      field: "name",
      width: 140,
      cellCls: "b-resourceinfo-cell",
      editor: VersionHelper.isTestEnv ? false : "text"
    };
  }
  construct(...args) {
    super.construct(...args);
    this.avatarRendering = new AvatarRendering({
      element: this.grid.element
    });
  }
  doDestroy() {
    super.doDestroy();
    this.avatarRendering.destroy();
  }
  getImageURL(imageName) {
    const resourceImagePath = this.grid.resourceImagePath || "", parts = resourceImagePath.split("//"), urlPart = parts.length > 1 ? parts[1] : resourceImagePath, joined = StringHelper.joinPaths([urlPart || "", imageName || ""]);
    return parts.length > 1 ? parts[0] + "//" + joined : joined;
  }
  template(resourceRecord, value2) {
    const me = this, {
      showImage,
      showRole,
      showMeta,
      showEventCount,
      grid
    } = me, {
      timeAxis,
      resourceImageExtension = "",
      defaultResourceImageName
    } = grid, roleField = typeof showRole === "string" ? showRole : "role", count = showEventCount && resourceRecord.eventStore.getEvents({
      includeOccurrences: grid.enableRecurringEvents,
      resourceRecord,
      startDate: timeAxis.startDate,
      endDate: timeAxis.endDate
    }).length, showAvatar = resourceRecord.showAvatar !== false;
    let imageUrl;
    if (showAvatar && showImage && resourceRecord.image !== false) {
      if (resourceRecord.imageUrl) {
        imageUrl = resourceRecord.imageUrl;
      } else {
        const imageName = typeof showImage === "string" ? showImage : resourceRecord.image || value2 && me.useNameAsImageName && value2.toLowerCase() + resourceImageExtension || defaultResourceImageName || "";
        imageUrl = imageName && me.getImageURL(imageName);
        if (imageUrl && !imageName.includes(".")) {
          imageUrl += resourceImageExtension;
        }
      }
    }
    return {
      class: {
        "b-resource-info": 1,
        "b-no-avatar": !showAvatar
      },
      children: [
        showImage && showAvatar && me.avatarRendering.getResourceAvatar({
          resourceRecord,
          initials: resourceRecord.initials,
          color: resourceRecord.eventColor,
          iconCls: resourceRecord.iconCls,
          imageUrl,
          defaultImageUrl: defaultResourceImageName && this.getImageURL(defaultResourceImageName)
        }),
        showRole || showEventCount || showMeta ? {
          tag: "dl",
          children: [
            {
              tag: "dt",
              text: value2
            },
            showRole ? {
              tag: "dd",
              class: "b-resource-role",
              text: resourceRecord.getValue(roleField)
            } : null,
            showEventCount ? {
              tag: "dd",
              class: "b-resource-events",
              text: me.L("L{eventCountText}", count)
            } : null,
            showMeta ? {
              tag: "dd",
              class: "b-resource-meta",
              html: me.showMeta(resourceRecord)
            } : null
          ]
        } : value2
        // This becomes a text node, no HTML encoding needed
      ]
    };
  }
  defaultRenderer({ grid, record, cellElement, value: value2, isExport }) {
    let result;
    if (record.isSpecialRow) {
      result = "";
    } else if (isExport) {
      result = value2;
    } else {
      if (this.autoScaleThreshold && grid.rowHeight < this.autoScaleThreshold) {
        cellElement.style.fontSize = grid.rowHeight / 40 + "em";
      } else {
        cellElement.style.fontSize = "";
      }
      result = this.template(record, value2);
    }
    return result;
  }
};
__publicField(ResourceInfoColumn, "$name", "ResourceInfoColumn");
__publicField(ResourceInfoColumn, "type", "resourceInfo");
ColumnStore.registerColumnType(ResourceInfoColumn);
ResourceInfoColumn._$name = "ResourceInfoColumn";

// ../Scheduler/lib/Scheduler/column/ScaleColumn.js
var ScaleColumn = class extends Column {
  //endregion
  //region Constructor/Destructor
  onDestroy() {
    this.scaleWidget.destroy();
  }
  //endregion
  //region Internal
  set width(width) {
    super.width = width;
    this.scaleWidget.width = width;
  }
  get width() {
    return super.width;
  }
  applyValue(useProp, key, value2) {
    if (key === "scalePoints") {
      this.scaleWidget[key] = value2;
    }
    return super.applyValue(...arguments);
  }
  buildScaleWidget() {
    const me = this;
    const scaleWidget = new Scale({
      owner: me.grid,
      appendTo: me.grid.floatRoot,
      cls: "b-hide-offscreen",
      align: "right",
      scalePoints: me.scalePoints,
      monitorResize: false
    });
    Object.defineProperties(scaleWidget, {
      width: {
        get() {
          return me.width;
        },
        set(width) {
          this.element.style.width = `${width}px`;
          this._width = me.width;
        }
      },
      height: {
        get() {
          return this._height;
        },
        set(height) {
          this.element.style.height = `${height}px`;
          this._height = height;
        }
      }
    });
    scaleWidget.width = me.width;
    return scaleWidget;
  }
  get scaleWidget() {
    const me = this;
    if (!me._scaleWidget) {
      me._scaleWidget = me.buildScaleWidget();
    }
    return me._scaleWidget;
  }
  //endregion
  //region Render
  defaultRenderer({ cellElement, value: value2, scaleWidgetConfig, scaleWidget = this.scaleWidget }) {
    ObjectHelper.assign(scaleWidget, {
      scalePoints: value2 || this.scalePoints,
      height: this.grid.rowHeight
    }, scaleWidgetConfig);
    scaleWidget.refresh();
    const scaleCloneElement = scaleWidget.element.cloneNode(true);
    scaleCloneElement.removeAttribute("id");
    scaleCloneElement.classList.remove("b-hide-offscreen");
    cellElement.innerHTML = "";
    cellElement.appendChild(scaleCloneElement);
  }
  //endregion
};
//region Config
__publicField(ScaleColumn, "$name", "ScaleColumn");
__publicField(ScaleColumn, "type", "scale");
__publicField(ScaleColumn, "isScaleColumn", true);
__publicField(ScaleColumn, "fields", [
  /**
   * Array of objects representing scale points. If not provided the column will try
   * reading points from the displayed record {@link #config-field} (`scalePoints` field by default).
   *
   * ```javascript
   * new TimelineHistogram({
   *     columns : {
   *         type        : 'scale',
   *         scalePoints : [
   *             { text : '8h', value : 8 },
   *             { text : '16h', value : 16 },
   *             { text : '24h', value : 24 }
   *         ]
   *     }
   * })
   * ```
   * @config {ScalePoint[]} scalePoints
   */
  "scalePoints"
]);
__publicField(ScaleColumn, "defaults", {
  text: "\xA0",
  width: 40,
  minWidth: 40,
  /**
   * The {@link Core/data/field/DataField#config-name} of the {@link Core/data/Model data model} field to
   * read scale point values from.
   *
   * @prp {String} field
   * @readonly
   * @category Common
   * @default "scalePoints"
   */
  field: "scalePoints",
  cellCls: "b-scale-cell",
  editor: false,
  sortable: false,
  groupable: false,
  filterable: false,
  alwaysClearCell: false,
  scalePoints: null
});
ColumnStore.registerColumnType(ScaleColumn);
ScaleColumn._$name = "ScaleColumn";

// ../Scheduler/lib/Scheduler/model/ResourceTickModel.js
var ResourceTickModel = class extends TimeSpan {
  constructor() {
    super(...arguments);
    // Flag checked by rendering to distinguish from event records
    __publicField(this, "isTickCell", true);
  }
  // Used for faster lookup during rendering
  get indexKey() {
    return `${this.startDate.getTime()}-${this.resourceId}`;
  }
  async setValue(value2) {
    this.value = value2;
  }
};
__publicField(ResourceTickModel, "$name", "ResourceTickModel");
__publicField(ResourceTickModel, "fields", [
  /**
   * Id of the resource this tick is associated with
   * @field {String|Number} resourceId
   */
  "resourceId",
  /**
   * Value of the resource tick
   * @field {Object|Number} value
   */
  "value"
]);
ResourceTickModel._$name = "ResourceTickModel";

// ../Scheduler/lib/Scheduler/data/ResourceTickStore.js
var ResourceTickStore = class extends AjaxStore {
  get $lazyLoadPluginClass() {
    return this.isPaged ? PagedDateStoreLazyLoadPlugin : DateStoreLazyLoadPlugin;
  }
};
__publicField(ResourceTickStore, "$name", "ResourceTickStore");
__publicField(ResourceTickStore, "defaultConfig", {
  /**
   * CrudManager must load stores in the correct order. Lowest first.
   * @private
   */
  loadPriority: 500,
  /**
   * CrudManager must sync stores in the correct order. Lowest first.
   * @private
   */
  syncPriority: 500,
  modelClass: ResourceTickModel,
  id: "resourceTicks"
});
ResourceTickStore._$name = "ResourceTickStore";

// ../Scheduler/lib/Scheduler/data/util/recurrence/RecurrenceLegend.js
var RecurrenceLegend = class extends Localizable_default() {
  static get allDaysValueAsArray() {
    return ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
  }
  static get allDaysValue() {
    return this.allDaysValueAsArray.join(",");
  }
  static get workingDaysValue() {
    return this.allDaysValueAsArray.filter((day2, index) => !DateHelper.nonWorkingDays[index]).join(",");
  }
  static get nonWorkingDaysValue() {
    return this.allDaysValueAsArray.filter((day2, index) => DateHelper.nonWorkingDays[index]).join(",");
  }
  /**
   * Returns the provided recurrence description. The recurrence might be assigned to a timespan model,
   * in this case the timespan start date should be provided in the second argument.
   * @param {Scheduler.model.RecurrenceModel} recurrenceRecurrence model.
   * @param {Date} [timeSpanStartDate] The recurring timespan start date. Can be omitted if the recurrence is assigned
   * to a timespan model (and the timespan has {@link Scheduler.model.TimeSpan#field-startDate} filled). Then start
   * date will be retrieved from the model.
   * @returns {String} The recurrence description.
   */
  static getLegend(recurrence, timeSpanStartDate) {
    const me = this, { timeSpan, interval, days: days2, monthDays, months, positions } = recurrence, startDate = timeSpanStartDate || timeSpan.startDate, tplData = { interval };
    let fn2;
    switch (recurrence.frequency) {
      case "DAILY":
        return interval === 1 ? me.L("L{Daily}") : me.L("L{Every {0} days}", tplData);
      case "WEEKLY":
        if (days2 && days2.length) {
          tplData.days = me.getDaysLegend(days2);
        } else if (startDate) {
          tplData.days = DateHelper.getDayName(startDate.getDay());
        }
        return me.L(interval === 1 ? "L{Weekly on {1}}" : "L{Every {0} weeks on {1}}", tplData);
      case "MONTHLY":
        if (days2 && days2.length && positions && positions.length) {
          tplData.days = me.getDaysLegend(days2, positions);
        } else if (monthDays && monthDays.length) {
          monthDays.sort((a, b) => a - b);
          tplData.days = me.arrayToText(monthDays);
        } else if (startDate) {
          tplData.days = startDate.getDate();
        }
        return me.L(interval === 1 ? "L{Monthly on {1}}" : "L{Every {0} months on {1}}", tplData);
      case "YEARLY":
        if (days2 && days2.length && positions && positions.length) {
          tplData.days = me.getDaysLegend(days2, positions);
        } else {
          tplData.days = startDate.getDate();
        }
        if (months && months.length) {
          months.sort((a, b) => a - b);
          if (months.length > 2) {
            fn2 = (month2) => DateHelper.getMonthShortName(month2 - 1);
          } else {
            fn2 = (month2) => DateHelper.getMonthName(month2 - 1);
          }
          tplData.months = me.arrayToText(months, fn2);
        } else {
          tplData.months = DateHelper.getMonthName(startDate.getMonth());
        }
        return me.L(interval === 1 ? "L{Yearly on {1} of {2}}" : "L{Every {0} years on {1} of {2}}", tplData);
    }
  }
  static getDaysLegend(days2, positions) {
    const me = this, tplData = { position: "" };
    let fn2;
    if (positions && positions.length) {
      tplData.position = me.arrayToText(positions, (position) => me.L(`L{position${position}}`));
    }
    if (days2.length) {
      days2.sort((a, b) => RecurrenceDayRuleEncoder.decodeDay(a)[0] - RecurrenceDayRuleEncoder.decodeDay(b)[0]);
      switch (days2.join(",")) {
        case me.allDaysValue:
          tplData.days = me.L("L{day}");
          break;
        case me.workingDaysValue:
          tplData.days = me.L("L{weekday}");
          break;
        case me.nonWorkingDaysValue:
          tplData.days = me.L("L{weekend day}");
          break;
        default:
          if (days2.length > 2) {
            fn2 = (day2) => DateHelper.getDayShortName(RecurrenceDayRuleEncoder.decodeDay(day2)[0]);
          } else {
            fn2 = (day2) => DateHelper.getDayName(RecurrenceDayRuleEncoder.decodeDay(day2)[0]);
          }
          tplData.days = me.arrayToText(days2, fn2);
      }
    }
    return me.L("L{daysFormat}", tplData);
  }
  // Converts array of items to a human readable list.
  // For example: [1,2,3,4]
  // to: "1, 2, 3 and 4"
  static arrayToText(array, fn2) {
    if (fn2) {
      array = array.map(fn2);
    }
    return array.join(", ").replace(/,(?=[^,]*$)/, this.L("L{ and }"));
  }
};
__publicField(RecurrenceLegend, "$name", "RecurrenceLegend");
RecurrenceLegend._$name = "RecurrenceLegend";

// ../Scheduler/lib/Scheduler/tooltip/ClockTemplate.js
var ClockTemplate = class extends Base {
  static get defaultConfig() {
    return {
      minuteHeight: 8,
      minuteTop: 2,
      hourHeight: 8,
      hourTop: 2,
      handLeft: 10,
      div: document.createElement("div"),
      scheduler: null,
      // may be passed to the constructor if needed
      // `b-sch-clock-day` for calendar icon
      // `b-sch-clock-hour` for clock icon
      template(data) {
        return `<div class="b-sch-clockwrap b-sch-clock-${data.mode || this.mode} ${data.cls || ""}">
                    <div class="b-sch-clock">
                        <div class="b-sch-hour-indicator">${data.date ? DateHelper.format(data.date, "MMM") : ""}</div>
                        <div class="b-sch-minute-indicator">${data.date ? DateHelper.format(data.date, "D") : ""}</div>
                        <div class="b-sch-clock-dot"></div>
                    </div>
                    <span class="b-sch-clock-text">${StringHelper.encodeHtml(data.text || "")}</span>
                </div>`;
      }
    };
  }
  generateContent(data) {
    return this.div.innerHTML = this.template(data);
  }
  updateDateIndicator(el, date2) {
    const hourIndicatorEl = el == null ? void 0 : el.querySelector(".b-sch-hour-indicator"), minuteIndicatorEl = el == null ? void 0 : el.querySelector(".b-sch-minute-indicator");
    if (date2 && hourIndicatorEl && minuteIndicatorEl && BrowserHelper.isBrowserEnv) {
      if (this.mode === "hour") {
        hourIndicatorEl.style.transform = `rotate(${date2.getHours() % 12 * 30}deg)`;
        minuteIndicatorEl.style.transform = `rotate(${date2.getMinutes() * 6}deg)`;
      } else {
        hourIndicatorEl.style.transform = "none";
        minuteIndicatorEl.style.transform = "none";
      }
    }
  }
  set mode(mode) {
    this._mode = mode;
  }
  // `day` mode for calendar icon
  // `hour` mode for clock icon
  get mode() {
    if (this._mode) {
      return this._mode;
    }
    const unitLessThanDay = DateHelper.compareUnits(this.scheduler.timeAxisViewModel.timeResolution.unit, "day") < 0, formatContainsHourInfo = DateHelper.formatContainsHourInfo(this.scheduler.displayDateFormat);
    return unitLessThanDay && formatContainsHourInfo ? "hour" : "day";
  }
  set template(template) {
    this._template = template;
  }
  /**
   * Get the clock template, which accepts an object of format { date, text }
   * @property {Function}
   * @param {*} Format object
   * @returns {String}
   */
  get template() {
    return this._template;
  }
};
ClockTemplate._$name = "ClockTemplate";

// ../Scheduler/lib/Scheduler/feature/base/DragBase.js
var DragBase = class extends InstancePlugin {
  //region Config
  static get defaultConfig() {
    return {
      // documented on Schedulers EventDrag feature and Gantt's TaskDrag
      tooltipTemplate: (data) => `
                <div class="b-sch-tip-${data.valid ? "valid" : "invalid"}">
                    ${data.startClockHtml}
                    ${data.endClockHtml}
                    <div class="b-sch-tip-message">${data.message}</div>
                </div>
            `,
      /**
       * Specifies whether to show tooltip while dragging event
       * @prp {Boolean}
       * @default
       */
      showTooltip: true,
      /**
       * When enabled, the event being dragged always "snaps" to the exact start date that it will have after drop.
       * @config {Boolean}
       * @default
       */
      showExactDropPosition: false,
      /*
       * The store from which the dragged items are mapped to the UI.
       * In Scheduler's implementation of this base class, this will be
       * an EventStore, in Gantt's implementations, this will be a TaskStore.
       * Because both derive from this base, we must refer to it as this.store.
       * @private
       */
      store: null,
      /**
       * An object used to configure the internal {@link Core.helper.DragHelper} class
       * @config {DragHelperConfig}
       */
      dragHelperConfig: null,
      tooltipCls: "b-eventdrag-tooltip",
      /**
       * Whether to allow the SNET constraint generated by an event drag to be placed in non-
       * working time. When `true` and the event is dragged so that its `startDate` is in non-working
       * time, the SNET constraint date will be kept on the non-working date where the task is dropped.
       * When `false`, snaps the SNET date to working time in the same way as the `startDate`. The
       * `startDate` is not affected by this config.
       *
       * Applies only to Scheduler Pro and Gantt.
       *
       * @config {Boolean}
       * @default false
       */
      allowNonWorkingTimeSNET: null
    };
  }
  static get configurable() {
    return {
      /**
       * Set to `false` to allow dragging tasks outside the client Scheduler.
       * Useful when you want to drag tasks between multiple Scheduler instances
       * @config {Boolean}
       * @default
       */
      constrainDragToTimeline: true,
      // documented on Schedulers EventDrag feature, not used for Gantt
      constrainDragToResource: true,
      constrainDragToTimeSlot: false,
      /**
       * Yields the {@link Core.widget.Tooltip} which tracks the event during a drag operation.
       * @member {Core.widget.Tooltip} tip
       */
      /**
       * A config object to allow customization of the {@link Core.widget.Tooltip} which tracks
       * the event during a drag operation.
       * @config {TooltipConfig}
       */
      tip: {
        $config: ["lazy", "nullify"],
        value: {
          align: {
            align: "b-t",
            allowTargetOut: true
          },
          autoShow: true,
          updateContentOnMouseMove: true
        }
      },
      /**
       * The `eventDrag`and `taskDrag` events are normally only triggered when the drag operation will lead to a
       * change in date or assignment. By setting this config to `false`, that logic is bypassed to trigger events
       * for each native mouse move event.
       * @prp {Boolean}
       */
      throttleDragEvent: true,
      capitalizedEventName: null,
      eventName: null
    };
  }
  // Plugin configuration. This plugin chains some of the functions in Grid.
  static get pluginConfig() {
    return {
      chain: ["onInternalPaint"]
    };
  }
  //endregion
  //region Init
  afterConstruct(...args) {
    const me = this, { client } = this;
    me.capitalizedEventName = me.capitalizedEventName || client.capitalizedEventName;
    me.eventName = me.eventName || client.scheduledEventName;
    super.afterConstruct(...args);
  }
  internalSnapToPosition(snapTo) {
    var _a5;
    const { dragData } = this;
    (_a5 = this.snapToPosition) == null ? void 0 : _a5.call(this, {
      assignmentRecord: dragData.assignmentRecord,
      eventRecord: dragData.eventRecord,
      resourceRecord: dragData.newResource || dragData.resourceRecord,
      startDate: dragData.startDate,
      endDate: dragData.endDate,
      snapTo
    });
  }
  buildDragHelperConfig() {
    const me = this, {
      client,
      constrainDragToTimeline,
      constrainDragToResource,
      constrainDragToTimeSlot,
      dragHelperConfig = {}
    } = me, { timeAxisViewModel, isHorizontal } = client, lockY = isHorizontal ? constrainDragToResource : constrainDragToTimeSlot, lockX = isHorizontal ? constrainDragToTimeSlot : constrainDragToResource;
    if (me.externalDropTargetSelector) {
      dragHelperConfig.dropTargetSelector = `.b-timeaxissubgrid,${me.externalDropTargetSelector}`;
    }
    return Objects.merge({
      name: me.constructor.name,
      // useful when debugging with multiple draggers
      positioning: "inset",
      rtlSource: client,
      lockX,
      lockY,
      minX: true,
      // Allows dropping with start before time axis
      maxX: true,
      // Allows dropping with end after time axis
      constrain: false,
      cloneTarget: !constrainDragToTimeline,
      // If we clone event dragged bars, we assume ownership upon drop so we can reuse the element and have animations
      removeProxyAfterDrop: false,
      dragWithin: constrainDragToTimeline ? null : client.floatRoot,
      hideOriginalElement: true,
      dropTargetSelector: ".b-timelinebase",
      allowDropOutside: !constrainDragToTimeline,
      // A CSS class added to drop target while dragging events
      dropTargetCls: me.externalDropTargetSelector ? "b-drop-target" : "",
      outerElement: client.timeAxisSubGridElement,
      targetSelector: client.eventSelector,
      scrollManager: constrainDragToTimeline ? client.scrollManager : null,
      createProxy: (el) => me.createProxy(el),
      snapCoordinates: ({ element, newX, newY }) => {
        var _a5, _b2, _c2, _d, _e;
        const { dragData } = me;
        if (client && me.constrainDragToTimeline && !me.constrainDragToTimeSlot && (me.showExactDropPosition || timeAxisViewModel.snap)) {
          const { timeAxis } = client, draggedEventRecord = (_a5 = dragData.record.event) != null ? _a5 : dragData.record, { calendar } = draggedEventRecord, { startDate } = dragData, coordinate = me.getCoordinate(draggedEventRecord, element, [newX, newY]);
          let snappedDate;
          if (client.fillTicks && timeAxis.isContinuous) {
            snappedDate = client.snap ? timeAxis.roundDate(startDate) : startDate;
          } else {
            snappedDate = timeAxisViewModel.getDateFromPosition(coordinate, "round");
            if (client.snapRelativeToEventStartDate || client.snap && client.fillTicks) {
              snappedDate = timeAxis.roundDate(snappedDate, startDate);
            }
          }
          if (!calendar || snappedDate && calendar.isWorkingTime(snappedDate, DateHelper.add(snappedDate, draggedEventRecord.fullDuration))) {
            const snappedPosition = snappedDate && timeAxisViewModel.getPositionFromDate(snappedDate);
            if (snappedDate && snappedDate >= client.startDate && snappedPosition != null) {
              if (isHorizontal) {
                newX = snappedPosition;
              } else {
                newY = snappedPosition;
              }
            }
          }
          if (client.snap && me.snapToResource && !constrainDragToResource) {
            let resourceHover;
            if (isHorizontal) {
              const rowIdHover = (_b2 = client.rowManager.getRowAt(dragData.context.clientY)) == null ? void 0 : _b2.id;
              resourceHover = client.resourceStore.getById(rowIdHover);
            } else {
              const pointerX = dragData.context.clientX - client.timeAxisSubGrid.rectangle().x, { first, last } = client.visibleResources;
              resourceHover = client.vertical.allResourceRecords.filter((x) => x.id >= first.id && x.id <= last.id).find((x) => x.instanceMeta(client).insetStart <= pointerX && x.instanceMeta(client).insetEnd > pointerX);
            }
            const resourceRecord = resourceHover || dragData.newResource || dragData.resourceRecord, { start: resourceMargin } = client.getResourceMarginObject(resourceRecord), rowHeight = isHorizontal ? (_c2 = client.getRowFor(resourceRecord)) == null ? void 0 : _c2.height : client.getResourceWidth(resourceRecord), rowTop = isHorizontal ? (_d = client.getRowFor(resourceRecord)) == null ? void 0 : _d.top : (_e = resourceRecord.instanceMeta(client)) == null ? void 0 : _e.insetStart;
            if (rowHeight !== void 0 && rowTop !== void 0) {
              let eventTop = rowTop + resourceMargin, distanceToCheck = me.snapToResource.threshold ? Math.min(rowHeight, me.snapToResource.threshold) : rowHeight / 4 + resourceMargin, distanceFromEventTop = isHorizontal ? Math.abs(newY - rowTop) : Math.abs(newX - rowTop);
              if (resourceRecord.id === dragData.resourceRecord.id) {
                eventTop = isHorizontal ? dragData.context.elementStartY : dragData.context.elementStartX;
                distanceFromEventTop = isHorizontal ? Math.abs(newY - eventTop) : Math.abs(newX - eventTop);
              }
              if (distanceFromEventTop <= distanceToCheck) {
                if (isHorizontal) {
                  newY = eventTop;
                } else {
                  newX = eventTop;
                }
              }
            }
          }
        }
        const snapTo = { x: newX, y: newY };
        me.internalSnapToPosition(snapTo);
        return snapTo;
      },
      internalListeners: {
        beforedragstart: "onBeforeDragStart",
        dragstart: "onDragStart",
        afterdragstart: "onAfterDragStart",
        drag: "onDrag",
        drop: "onDrop",
        abort: "onDragAbort",
        abortFinalized: "onDragAbortFinalized",
        reset: "onDragReset",
        thisObj: me
      }
    }, dragHelperConfig, {
      isElementDraggable: (el, event) => {
        return (!dragHelperConfig || !dragHelperConfig.isElementDraggable || dragHelperConfig.isElementDraggable(el, event)) && me.isElementDraggable(el, event);
      }
    });
  }
  /**
   * Called when scheduler is rendered. Sets up drag and drop and hover tooltip.
   * @private
   */
  onInternalPaint({ firstPaint }) {
    var _a5;
    const me = this, { client } = me;
    (_a5 = me.drag) == null ? void 0 : _a5.destroy();
    me.drag = DragHelper.new(me.buildDragHelperConfig());
    if (firstPaint) {
      client.rowManager.ion({
        changeTotalHeight: () => {
          var _a6;
          return me.updateYConstraint((_a6 = me.dragData) == null ? void 0 : _a6[`${client.scheduledEventName}Record`]);
        },
        thisObj: me
      });
    }
    if (me.showTooltip) {
      me.clockTemplate = new ClockTemplate({
        scheduler: client
      });
    }
  }
  doDestroy() {
    var _a5, _b2, _c2;
    (_a5 = this.drag) == null ? void 0 : _a5.destroy();
    (_b2 = this.clockTemplate) == null ? void 0 : _b2.destroy();
    (_c2 = this.tip) == null ? void 0 : _c2.destroy();
    super.doDestroy();
  }
  get tipId() {
    return `${this.client.id}-event-drag-tip`;
  }
  changeTip(tip, oldTip) {
    const me = this;
    if (tip) {
      const result = Tooltip.reconfigure(oldTip, Tooltip.mergeConfigs({
        forElement: me.element,
        id: me.tipId,
        getHtml: me.getTipHtml.bind(me),
        cls: me.tooltipCls,
        owner: me.client
      }, tip), {
        owner: me.client,
        defaults: {
          type: "tooltip"
        }
      });
      result.ion({ innerHtmlUpdate: "updateDateIndicator", thisObj: me });
      return result;
    } else {
      oldTip == null ? void 0 : oldTip.destroy();
    }
  }
  //endregion
  //region Drag events
  createProxy(element) {
    const proxy = element.cloneNode(true);
    delete proxy.id;
    proxy.classList.add(`b-sch-${this.client.mode}`);
    return proxy;
  }
  onBeforeDragStart({ context, event }) {
    var _a5;
    const me = this, { client } = me, dragData = me.getMinimalDragData(context, event), eventRecord = dragData == null ? void 0 : dragData[`${client.scheduledEventName}Record`], resourceRecord = dragData.resourceRecord, noDirectionOpen = me.constrainDragToResource && me.constrainDragToTimeSlot;
    if (client.readOnly || me.disabled || !eventRecord || eventRecord.isDraggable === false || eventRecord.readOnly || (resourceRecord == null ? void 0 : resourceRecord.readOnly) || noDirectionOpen) {
      return false;
    }
    context.pointerStartDate = client.getDateFromCoord({ coord: client.isVertical ? context.startPageY : context.startPageX, local: false });
    const result = me.triggerBeforeEventDrag(
      `before${me.capitalizedEventName}Drag`,
      {
        ...dragData,
        event,
        // to be deprecated
        context: {
          ...context,
          ...dragData
        }
      }
    ) !== false;
    if (result) {
      me.updateYConstraint(eventRecord, resourceRecord);
      (_a5 = client[`before${me.capitalizedEventName}DragStart`]) == null ? void 0 : _a5.call(client, context, dragData);
    }
    return result;
  }
  onAfterDragStart({ context, event }) {
  }
  /**
   * Returns true if a drag operation is active
   * @property {Boolean}
   * @readonly
   */
  get isDragging() {
    var _a5;
    return (_a5 = this.drag) == null ? void 0 : _a5.isDragging;
  }
  // Checked by dependencies to determine if live redrawing is needed
  get isActivelyDragging() {
    return this.isDragging && !this.finalizing;
  }
  /**
   * Triggered when dragging of an event starts. Initializes drag data associated with the event being dragged.
   * @private
   */
  onDragStart({ context, event }) {
    var _a5, _b2, _c2;
    const me = this, client = (_a5 = me.findClientFromTarget(event, context)) != null ? _a5 : me.client;
    me.currentOverClient = client;
    me.drag.unifiedProxy = me.unifiedDrag;
    me.onMouseOverNewTimeline(client, true);
    const dragData = me.dragData = me.getDragData(context);
    me.suspendElementRedrawing(context.element);
    if (me.showTooltip && me.tip) {
      const tipTarget = dragData.context.dragProxy ? dragData.context.dragProxy.firstChild : context.element;
      me.tip.showBy(tipTarget);
    }
    me.triggerDragStart(dragData);
    (_b2 = client[`after${me.capitalizedEventName}DragStart`]) == null ? void 0 : _b2.call(client, context, dragData);
    const {
      eventMenu,
      taskMenu
    } = client.features, menuFeature = eventMenu || taskMenu;
    (_c2 = menuFeature == null ? void 0 : menuFeature.hideContextMenu) == null ? void 0 : _c2.call(menuFeature, false);
  }
  updateDateIndicator() {
    const { startDate, endDate } = this.dragData, { tip, clockTemplate } = this, endDateElement = tip.element.querySelector(".b-sch-tooltip-enddate");
    clockTemplate.updateDateIndicator(tip.element, startDate);
    endDateElement && clockTemplate.updateDateIndicator(endDateElement, endDate);
  }
  findClientFromTarget(event, context) {
    let { target } = event;
    if (/^touch/.test(event.type)) {
      const center = Rectangle.from(context.element, null, true).center;
      target = DomHelper.elementFromPoint(center.x, center.y);
    }
    const client = Widget.fromElement(target, "timelinebase");
    return (client == null ? void 0 : client.isResourceHistogram) ? null : client;
  }
  /**
   * Triggered while dragging an event. Updates drag data, validation etc.
   * @private
   */
  onDrag({ context, event }) {
    const me = this, dd = me.dragData, start = dd.startDate;
    let client;
    if (me.constrainDragToTimeline) {
      client = me.client;
    } else {
      client = me.findClientFromTarget(event, dd.context);
    }
    me.updateDragContext(context, event);
    if (!client) {
      return;
    }
    if (client !== me.currentOverClient) {
      me.onMouseOverNewTimeline(client);
    }
    if (dd.dirty || !me.throttleDragEvent) {
      const valid = dd.valid;
      me.triggerEventDrag(dd, start);
      if (valid !== dd.valid) {
        dd.context.valid = dd.externalDragValidity = dd.valid;
      }
    }
    if (me.showTooltip && me.tip) {
      me.tip.lastAlignSpec.allowTargetOut = !dd.valid;
      me.tip.realign();
    }
  }
  onMouseOverNewTimeline(newTimeline, initial) {
    const me = this, { drag: { lockX, lockY } } = me, scrollables = [];
    me.currentOverClient.element.classList.remove("b-dragging-" + me.currentOverClient.scheduledEventName);
    newTimeline.element.classList.add("b-dragging-" + newTimeline.scheduledEventName);
    if (!initial) {
      me.currentOverClient.scrollManager.stopMonitoring();
    }
    if (!lockX) {
      scrollables.push({
        element: newTimeline.timeAxisSubGrid.scrollable.element,
        direction: "horizontal"
      });
    }
    if (!lockY) {
      scrollables.push({
        element: newTimeline.scrollable.element,
        direction: "vertical"
      });
    }
    newTimeline.scrollManager.startMonitoring({
      scrollables,
      callback: me.drag.onScrollManagerScrollCallback
    });
    me.currentOverClient = newTimeline;
  }
  triggerBeforeEventDropFinalize(eventType, eventData, client) {
    client.trigger(eventType, eventData);
  }
  /**
   * Triggered when dropping an event. Finalizes the operation.
   * @private
   */
  onDrop({ context, event }) {
    var _a5;
    const me = this, { currentOverClient, dragData } = me;
    let modified = false;
    currentOverClient == null ? void 0 : currentOverClient.scrollManager.stopMonitoring();
    (_a5 = me.tip) == null ? void 0 : _a5.hide();
    context.valid = context.valid && me.isValidDrop(dragData);
    me.drag.removeProxyAfterDrop = Boolean(dragData.externalDropTarget);
    if (context.valid && dragData.startDate && dragData.endDate) {
      let beforeDropTriggered = false;
      dragData.finalize = async (valid) => {
        if (beforeDropTriggered || dragData.async) {
          await me.finalize(valid);
        } else {
          context.valid = context.valid && valid;
        }
      };
      me.triggerBeforeEventDropFinalize(`before${me.capitalizedEventName}DropFinalize`, {
        context: dragData,
        domEvent: event
      }, currentOverClient);
      beforeDropTriggered = true;
      context.async = dragData.async;
      if (!context.async && !dragData.externalDropTarget) {
        modified = dragData.startDate - dragData.origStart !== 0 || dragData.newResource !== dragData.resourceRecord;
      }
    }
    if (!context.async) {
      me.finalize(dragData.valid && context.valid && modified);
    }
  }
  onDragAbort({ context }) {
    var _a5, _b2;
    const me = this;
    me.isAborting = true;
    (_a5 = me.currentOverClient) == null ? void 0 : _a5.scrollManager.stopMonitoring();
    me.client.currentOrientation.onDragAbort({ context, dragData: me.dragData });
    me.resetDraggedElements();
    (_b2 = me.tip) == null ? void 0 : _b2.hide();
    me.triggerDragAbort(me.dragData);
  }
  // Fired after any abort animation has completed (the point where we want to trigger redraw of progress lines etc)
  onDragAbortFinalized({ context }) {
    var _a5, _b2;
    const me = this;
    me.triggerDragAbortFinalized(me.dragData);
    (_b2 = (_a5 = me.client)[`after${me.capitalizedEventName}DragAbortFinalized`]) == null ? void 0 : _b2.call(_a5, context, me.dragData);
    me.isAborting = false;
  }
  // For the drag across multiple schedulers, tell all involved scroll managers to stop monitoring
  onDragReset({ source: dragHelper }) {
    var _a5;
    const me = this, currentTimeline = me.currentOverClient;
    if ((_a5 = dragHelper.context) == null ? void 0 : _a5.started) {
      me.resetDraggedElements();
      currentTimeline.trigger(`${me.eventName}DragReset`);
    }
    currentTimeline == null ? void 0 : currentTimeline.element.classList.remove(`b-dragging-${currentTimeline.scheduledEventName}`);
    me.dragData = null;
  }
  resetDraggedElements() {
    const { dragData } = this, { eventBarEls, draggedEntities } = dragData;
    this.resumeRecordElementRedrawing(dragData.record);
    draggedEntities.forEach((record, i) => {
      this.resumeRecordElementRedrawing(record);
      if (eventBarEls[i]) {
        eventBarEls[i].classList.remove(this.drag.draggingCls);
        eventBarEls[i].retainElement = false;
      }
    });
    dragData.context.element.retainElement = false;
  }
  /**
   * Triggered internally on invalid drop.
   * @private
   */
  onInternalInvalidDrop(abort) {
    var _a5;
    const me = this, { context } = me.drag;
    (_a5 = me.tip) == null ? void 0 : _a5.hide();
    me.triggerAfterDrop(me.dragData, false);
    context.valid = false;
    if (abort) {
      me.drag.abort();
    }
  }
  //endregion
  //region Finalization & validation
  /**
   * Called on drop to update the record of the event being dropped.
   * @private
   * @param {Boolean} updateRecords Specify true to update the record, false to treat as invalid
   */
  async finalize(updateRecords) {
    const me = this, { dragData, currentOverClient } = me, { eventBarEls } = dragData, clientEventTipFeature = currentOverClient.features.taskTooltip || currentOverClient.features.eventTooltip;
    if (!dragData || me.finalizing) {
      return;
    }
    const { context, draggedEntities, externalDropTarget } = dragData;
    let result;
    me.finalizing = true;
    draggedEntities.forEach((record, i) => {
      me.resumeRecordElementRedrawing(record);
      if (eventBarEls[i]) {
        eventBarEls[i].classList.remove(me.drag.draggingCls);
        eventBarEls[i].retainElement = false;
      }
    });
    context.element.retainElement = false;
    if (externalDropTarget && dragData.valid || updateRecords) {
      result = me.updateRecords(dragData);
      if (!externalDropTarget && Objects.isPromise(result)) {
        context.async = true;
        await result;
      }
      if (!dragData.valid) {
        me.onInternalInvalidDrop(true);
      } else {
        if (context.async) {
          context.finalize();
        }
        if (externalDropTarget) {
          me.client.refreshRows(null, false);
        }
        me.triggerAfterDrop(dragData, true);
      }
    } else {
      me.onInternalInvalidDrop(context.async || dragData.async);
    }
    me.finalizing = false;
    if (clientEventTipFeature == null ? void 0 : clientEventTipFeature.enabled) {
      clientEventTipFeature.disabled = true;
      currentOverClient.setTimeout(() => {
        clientEventTipFeature.disabled = false;
      }, 200);
    }
    return result;
  }
  //endregion
  //region Drag data
  getEventStartEndDatesFromGraph(eventRecord, startDate, toClient) {
    let endDate;
    const { newResource, resourceRecord, assignmentRecords } = this.dragData, targetProject = toClient.project, targetGraph = targetProject.graph, isCrossProject = targetProject !== eventRecord.project, branch = targetGraph.branch({
      autoCommit: false,
      onComputationCycle: "throw",
      project: targetProject
    });
    if (isCrossProject) {
      eventRecord = eventRecord.copy();
      eventRecord.parentEvent = targetProject.eventStore.rootNode;
      eventRecord.project = targetProject;
      branch.addEntity(eventRecord);
      for (const assignment of assignmentRecords) {
        branch.addEntity(assignment.copy({
          eventId: eventRecord.id,
          event: eventRecord,
          resourceId: newResource.id,
          resource: newResource
        }));
      }
    } else {
      if (newResource && newResource !== resourceRecord) {
        for (const assignment of assignmentRecords) {
          branch.write(assignment.$.resource, newResource);
        }
      }
    }
    branch.write(eventRecord.$.startDate, startDate);
    try {
      startDate = branch.read(eventRecord.$.startDate);
      endDate = branch.read(eventRecord.$.endDate);
    } catch (e) {
      return false;
    } finally {
      branch.clearBranch();
    }
    return { startDate, endDate };
  }
  getEventNewStartEndDates(eventRecord, timeDiff, toClient) {
    let startDate = this.adjustStartDate(eventRecord.startDate, timeDiff), endDate;
    if (eventRecord.graph && !this.allowNonWorkingTimeSNET && (!eventRecord.manuallyScheduled || eventRecord.project.skipNonWorkingTimeWhenSchedulingManually)) {
      try {
        ({ startDate, endDate } = this.getEventStartEndDatesFromGraph(eventRecord, startDate, toClient));
      } catch (e) {
        return { valid: false };
      }
    } else {
      endDate = DateHelper.add(startDate, eventRecord.fullDuration);
    }
    return { startDate, endDate };
  }
  /**
   * Updates drag data's dates and validity (calls #validatorFn if specified)
   * @private
   */
  updateDragContext(info, event) {
    var _a5, _b2, _c2;
    const me = this, { drag } = me, dd = me.dragData, client = me.currentOverClient, { isHorizontal, timeAxis } = client, { record } = dd, eventRecord = record.isAssignment ? record.event : record, lastDragStartDate = dd.startDate, constrainToTimeSlot = me.constrainDragToTimeSlot || (isHorizontal ? drag.lockX : drag.lockY);
    dd.browserEvent = event;
    Object.assign(dd, me.getProductDragContext(dd));
    if (constrainToTimeSlot) {
      dd.timeDiff = 0;
    } else {
      let timeDiff;
      if (timeAxis.isContinuous || dd.startsOutsideView && dd.endsOutsideView) {
        const timeAxisPosition = client.isHorizontal ? (_a5 = info.pageX) != null ? _a5 : info.startPageX : (_b2 = info.pageY) != null ? _b2 : info.startPageY, pointerDate = client.getDateFromCoordinate(timeAxisPosition, null, false, true);
        timeDiff = dd.timeDiff = pointerDate - info.pointerStartDate;
        if (timeDiff !== null) {
          Object.assign(dd, me.getEventNewStartEndDates(eventRecord, timeDiff, client));
          if (dd.valid) {
            dd.timeDiff = dd.startDate - dd.origStart;
          }
        }
      } else {
        const range = me.resolveStartEndDates(info.element);
        dd.valid = Boolean(range.startDate && range.endDate);
        if (dd.valid) {
          timeDiff = range.startDate - dd.origStart;
        }
        if (timeDiff !== void 0) {
          if (eventRecord.graph && !me.allowNonWorkingTimeSNET) {
            Object.assign(dd, me.getEventStartEndDatesFromGraph(eventRecord, range.startDate, client));
          } else {
            dd.startDate = range.startDate;
            dd.endDate = range.endDate;
          }
        }
        dd.timeDiff = timeDiff;
      }
    }
    const positionDirty = dd.dirty = dd.dirty || lastDragStartDate - dd.startDate !== 0;
    if (dd.valid) {
      if (me.constrainDragToTimeline && (dd.endDate <= timeAxis.startDate || dd.startDate >= timeAxis.endDate)) {
        dd.valid = false;
        dd.context.message = me.L("L{EventDrag.noDropOutsideTimeline}");
      } else if (positionDirty || dd.externalDropTarget) {
        const result = dd.externalDragValidity = !event || info.pageX && me.checkDragValidity(dd, event);
        if (!result || typeof result === "boolean") {
          dd.valid = result !== false;
          dd.context.message = "";
        } else {
          dd.valid = result.valid !== false;
          dd.context.message = result.message;
        }
      } else {
        dd.valid = dd.externalDragValidity !== false && ((_c2 = dd.externalDragValidity) == null ? void 0 : _c2.valid) !== false;
      }
    } else {
      dd.valid = false;
    }
    dd.context.valid = dd.valid;
  }
  suspendRecordElementRedrawing(record, suspend = true) {
    this.suspendElementRedrawing(this.getRecordElement(record), suspend);
    record.instanceMeta(this.client).retainElement = suspend;
  }
  resumeRecordElementRedrawing(record) {
    this.suspendRecordElementRedrawing(record, false);
  }
  suspendElementRedrawing(element, suspend = true) {
    if (element) {
      element.retainElement = suspend;
    }
  }
  resumeElementRedrawing(element) {
    this.suspendElementRedrawing(element, false);
  }
  /**
   * Initializes drag data (dates, constraints, dragged events etc). Called when drag starts.
   * @private
   * @param info
   * @returns {*}
   */
  getDragData(info) {
    const me = this, { client, drag } = me, productDragData = me.setupProductDragData(info), {
      record,
      eventBarEls,
      draggedEntities
    } = productDragData, { startEvent } = drag, timespan = record.isAssignment ? record.event : record, origStart = timespan.startDate, origEnd = timespan.endDate, timeAxis = client.timeAxis, startsOutsideView = origStart < timeAxis.startDate, endsOutsideView = origEnd > timeAxis.endDate, multiSelect = client.isSchedulerBase ? Boolean(client.multiEventSelect) : client.selectionMode.multiSelect, coordinate = me.getCoordinate(timespan, info.element, [info.elementStartX, info.elementStartY]), clientCoordinate = me.getCoordinate(timespan, info.element, [info.startClientX, info.startClientY]);
    me.suspendRecordElementRedrawing(record);
    draggedEntities.forEach((record2) => me.suspendRecordElementRedrawing(record2));
    if (!client.selectionMode.checkboxOnly) {
      if (record.isAssignment) {
        client.selectAssignment(record, startEvent.ctrlKey && multiSelect);
      } else {
        client.selectEvent(record, startEvent.ctrlKey && multiSelect);
      }
    }
    const dragData = {
      context: info,
      ...productDragData,
      sourceDate: startsOutsideView ? origStart : client.getDateFromCoordinate(coordinate),
      screenSourceDate: client.getDateFromCoordinate(clientCoordinate, null, false),
      startDate: origStart,
      endDate: origEnd,
      timeDiff: 0,
      origStart,
      origEnd,
      startsOutsideView,
      endsOutsideView,
      duration: origEnd - origStart,
      browserEvent: startEvent
      // So we can know if SHIFT/CTRL was pressed
    };
    if (eventBarEls.length > 1) {
      info.relatedElements = eventBarEls.filter((el) => el && el !== drag.context.grabbed);
    }
    eventBarEls.forEach((el) => el == null ? void 0 : el.classList.remove("b-sch-event-hover", "b-active"));
    return dragData;
  }
  //endregion
  //region Constraints
  // private
  setupConstraints(constrainRegion, elRegion, tickSize, constrainX, constrainY) {
    const me = this, xTickSize = !me.showExactDropPosition && tickSize > 1 ? tickSize : 0, yTickSize = 0;
    if (constrainX) {
      me.setXConstraint(constrainRegion.left, constrainRegion.right - elRegion.width, xTickSize);
    } else {
      me.setXConstraint(true, true, xTickSize);
    }
    if (constrainY) {
      me.setYConstraint(constrainRegion.top, constrainRegion.bottom - elRegion.height, yTickSize);
    } else {
      me.setYConstraint(0, true, yTickSize);
    }
  }
  updateYConstraint(eventRecord, resourceRecord) {
    const me = this, { client } = me, { context } = me.drag, tickSize = client.timeAxisViewModel.snapPixelAmount;
    if (context && !me.drag.lockY) {
      let constrainRegion;
      if (me.constrainDragToTimeline) {
        constrainRegion = client.getScheduleRegion(resourceRecord, eventRecord);
      } else {
        me.setYConstraint(null, null, tickSize);
        return;
      }
      me.setYConstraint(
        constrainRegion.top,
        constrainRegion.bottom - context.element.offsetHeight,
        tickSize
      );
    } else {
      me.setYConstraint(null, null, tickSize);
    }
  }
  setXConstraint(iLeft, iRight, iTickSize) {
    const { drag } = this;
    drag.minX = iLeft;
    drag.maxX = iRight;
  }
  setYConstraint(iUp, iDown, iTickSize) {
    const { drag } = this;
    drag.minY = iUp;
    drag.maxY = iDown;
  }
  //endregion
  //region Other stuff
  adjustStartDate(startDate, timeDiff) {
    const { currentOverClient } = this, rounded = currentOverClient.timeAxis.roundDate(
      new Date(startDate - 0 + timeDiff),
      currentOverClient.snapRelativeToEventStartDate || currentOverClient.snap && currentOverClient.fillTicks ? startDate : false
    );
    if (currentOverClient.fillTicks && (currentOverClient.timeAxis.mainUnit === "day" || DateHelper.compareUnits(currentOverClient.timeResolution.unit, "d") === 0)) {
      DateHelper.copyTimeValues(rounded, this.dragData.origStart);
    }
    return this.constrainStartDate(rounded);
  }
  normalizeDraggedElement(draggedElement) {
    return DomHelper.down(draggedElement, this.currentOverClient.eventInnerSelector);
  }
  resolveStartEndDates(draggedElement) {
    const timeline = this.currentOverClient, { timeAxis } = timeline, normalizedElement = this.normalizeDraggedElement(draggedElement), proxyRect = Rectangle.from(normalizedElement, timeline.timeAxisSubGridElement), dd = this.dragData, [record] = dd.draggedEntities, eventRecord = record.isAssignment ? record.event : record, fillSnap = timeline.fillTicks && timeline.snapRelativeToEventStartDate, totalDurationMS = eventRecord.endDate - eventRecord.startDate;
    let {
      start: startDate,
      end: endDate
    } = timeline.getStartEndDatesFromRectangle(proxyRect, fillSnap ? null : "round", totalDurationMS, true);
    if (startDate && endDate) {
      if (fillSnap) {
        const offsetMS = eventRecord.startDate - DateHelper.startOf(eventRecord.startDate, timeAxis.unit), proxyMS = endDate - startDate, offsetPx = offsetMS / proxyMS * proxyRect.width;
        proxyRect.deflate(timeline.isVertical ? offsetPx : 0, 0, 0, timeline.isHorizontal ? offsetPx : 0);
        const proxyStart = proxyRect.getStart(timeline.rtl, !timeline.isVertical);
        startDate = timeline.getDateFromCoordinate(proxyStart, null, true);
        startDate = timeAxis.roundDate(startDate, eventRecord.startDate);
      }
      startDate = this.adjustStartDate(startDate, 0);
      if (!dd.startsOutsideView) {
        if (!timeAxis.dateInAxis(startDate, false)) {
          const tick = timeAxis.getTickFromDate(startDate);
          if (tick >= 0) {
            startDate = timeAxis.getDateFromTick(tick);
          }
        }
        endDate = startDate && DateHelper.add(startDate, totalDurationMS);
      } else if (!dd.endsOutsideView) {
        startDate = endDate && DateHelper.add(endDate, -totalDurationMS);
      }
    }
    return {
      startDate,
      endDate
    };
  }
  //endregion
  //region Dragtip
  /**
   * Gets html to display in tooltip while dragging event. Uses clockTemplate to display start & end dates.
   */
  getTipHtml() {
    const me = this, { dragData, client, tooltipTemplate } = me, { startDate, endDate, draggedEntities, newResource } = dragData, startText = client.getFormattedDate(startDate), endText = endDate && client.getFormattedEndDate(endDate, startDate) || "", { valid, message, element, dragProxy } = dragData.context, tipTarget = dragProxy ? dragProxy.firstChild : element, dragged = draggedEntities[0], timeSpanRecord = dragged.isTask ? dragged : dragged.event;
    me.tip.lastAlignSpec.target = tipTarget;
    return tooltipTemplate({
      valid,
      startDate,
      endDate,
      startText,
      endText,
      dragData,
      newResource: newResource || dragData.resourceRecord,
      message: message || "",
      [client.scheduledEventName + "Record"]: timeSpanRecord,
      startClockHtml: me.clockTemplate.template({
        date: startDate,
        text: startText,
        cls: "b-sch-tooltip-startdate"
      }),
      endClockHtml: timeSpanRecord.isMilestone ? "" : me.clockTemplate.template({
        date: endDate,
        text: endText,
        cls: "b-sch-tooltip-enddate"
      })
    });
  }
  //endregion
  //region Configurable
  // Constrain to time slot means lockX if we're horizontal, otherwise lockY
  updateConstrainDragToTimeSlot(value2) {
    if (this.drag) {
      const axis = this.client.isHorizontal ? "lockX" : "lockY";
      this.drag[axis] = value2;
    }
  }
  // Constrain to resource means lockY if we're horizontal, otherwise lockX
  updateConstrainDragToResource(constrainDragToResource) {
    const me = this;
    if (me.drag) {
      const { constrainDragToTimeSlot } = me, { isHorizontal } = me.client;
      if (constrainDragToResource) {
        me.constrainDragToTimeline = true;
      }
      me.drag.lockY = isHorizontal ? constrainDragToResource : constrainDragToTimeSlot;
      me.drag.lockX = isHorizontal ? constrainDragToTimeSlot : constrainDragToResource;
    }
  }
  updateConstrainDragToTimeline(constrainDragToTimeline) {
    if (!this.isConfiguring) {
      Object.assign(this.drag, {
        cloneTarget: !constrainDragToTimeline,
        allowDropOutside: !constrainDragToTimeline,
        dragWithin: constrainDragToTimeline ? null : this.client.floatRoot,
        scrollManager: constrainDragToTimeline ? this.client.scrollManager : null
      });
    }
  }
  constrainStartDate(startDate) {
    const { dragData } = this, { dateConstraints } = dragData, scheduleableRecord = dragData.eventRecord || dragData.taskRecord || dragData.record;
    if (dateConstraints == null ? void 0 : dateConstraints.start) {
      startDate = DateHelper.max(dateConstraints.start, startDate);
    }
    if (dateConstraints == null ? void 0 : dateConstraints.end) {
      startDate = DateHelper.min(new Date(dateConstraints.end - scheduleableRecord.rawDurationMS), startDate);
    }
    return startDate;
  }
  //endregion
  //region Product specific, implemented in subclasses
  getElementFromContext(context) {
    return context.grabbed || context.dragProxy || context.element;
  }
  // Provide your custom implementation of this to allow additional selected records to be dragged together with the original one.
  getRelatedRecords(record) {
    return [];
  }
  getMinimalDragData(info, event) {
    return {};
  }
  // Check if element can be dropped at desired location
  isValidDrop(dragData) {
    throw new Error("Implement in subclass");
  }
  // Similar to the fn above but also calls validatorFn
  checkDragValidity(dragData) {
    throw new Error("Implement in subclass");
  }
  // Update records being dragged
  updateRecords(context) {
    throw new Error("Implement in subclass");
  }
  // Determine if an element can be dragged
  isElementDraggable(el, event) {
    throw new Error("Implement in subclass");
  }
  // Get coordinate for correct axis
  getCoordinate(record, element, coord) {
    throw new Error("Implement in subclass");
  }
  // Product specific drag data
  setupProductDragData(info) {
    throw new Error("Implement in subclass");
  }
  // Product specific data in drag context
  getProductDragContext(dd) {
    throw new Error("Implement in subclass");
  }
  getRecordElement(record) {
    throw new Error("Implement in subclass");
  }
  //endregion
};
DragBase._$name = "DragBase";

// ../Scheduler/lib/Scheduler/feature/mixin/TransactionalFeature.js
var TransactionalFeature_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base).mixin(AttachToProjectMixin_default) {
    //#region AttachToProjectMixin implementation
    detachFromProject(project) {
      this.rejectFeatureTransaction();
      super.detachFromProject(project);
    }
    //#endregion
    //#region TaskEditSTM Mixin overrides
    getStmCapture() {
      var _a6, _b2;
      const result = (_b2 = (_a6 = super.getStmCapture) == null ? void 0 : _a6.call(this)) != null ? _b2 : {};
      result._editorPromiseResolve = this._editorPromiseResolve;
      return result;
    }
    applyStmCapture(stmCapture) {
      var _a6;
      (_a6 = super.applyStmCapture) == null ? void 0 : _a6.call(this, stmCapture);
      this._editorPromiseResolve = stmCapture._editorPromiseResolve;
    }
    //#endregion
    //#region API related to passing transaction between features
    /**
     * Capturing transaction is a process of passing control over current revision boundaries from one feature
     * to another. At the moment it is only used by the CellEdit feature which keeps revision open while new
     * records are being created.
     * We take the promise which unblocks the queue, store transaction id and raise a flag which tells this
     * mixin to not actually start new or finish existing transaction. Rejection, however, cleans up the flag
     * and unblocks the queue like it should.
     * Workflow is like this:
     * ```
     * const capture = feature1.captureFeatureTransaction
     * feature2.applyCapturedFeatureTransaction(capture);
     * ```
     * @returns {Object}
     * @private
     */
    captureFeatureTransaction() {
      if (this.client.transactionalFeaturesEnabled) {
        this._hasFeatureTransactionCapture = true;
        return {
          source: this,
          resolve: this._editorPromiseResolve
        };
      }
    }
    /**
     * Applies captured feature transaction to pass control to the current feature
     * @param {Object} capture
     * @private
     */
    applyCapturedFeatureTransaction(capture = {}) {
      if (this.client.transactionalFeaturesEnabled) {
        const { source, resolve } = capture;
        if (source !== this) {
          source._hasFeatureTransactionCapture = false;
          source.cleanupTransactionMetadata();
          this._hasFeatureTransactionCapture = true;
          this._editorPromiseResolve = resolve;
        }
      }
    }
    releaseFeatureTransaction() {
      if (this.client.transactionalFeaturesEnabled) {
        this._hasFeatureTransactionCapture = false;
      }
    }
    //#endregion
    get canStartFeatureTransaction() {
      return !this.client.transactionalFeaturesEnabled || !this._hasFeatureTransactionCapture && !this.client.project.stm.isNavigatingRevisions;
    }
    async startFeatureTransaction() {
      if (!this.client.transactionalFeaturesEnabled || this._hasFeatureTransactionCapture) {
        return;
      }
      const me = this, { project } = me.client, { stm } = project, id2 = IdHelper.generateId("featureTransaction");
      me.client.trigger("beforeFeatureTransactionStart", { id: id2 });
      let chainResolved;
      if (me.hasStmCapture) {
        stm.startTransaction();
      } else {
        chainResolved = project.queue(async () => {
          if (!project.isEngineReady()) {
            await project.commitAsync();
          }
        });
      }
      project.queue(() => {
        if (!me.hasStmCapture) {
          me._stmInitiallyDisabled = stm.disabled;
          me._stmInitiallyAutoRecord = stm.autoRecord;
          if (stm.isRecording) {
            stm.stopTransaction();
          } else if (me._stmInitiallyDisabled) {
            stm.enable();
          }
          stm.autoRecord = false;
        }
        if (!stm.isRecording) {
          stm.startTransaction();
        }
        me.client.trigger("featureTransactionStart", { id: id2 });
        return new Promise((resolve) => me._editorPromiseResolve = resolve);
      });
      await chainResolved;
    }
    rejectFeatureTransaction() {
      var _a6;
      const me = this, { stm } = me.client.project, id2 = me.client.featureTransactionId;
      if (!id2 || me.finishingFeatureTransaction) {
        return;
      }
      (_a6 = me._editorPromiseResolve) == null ? void 0 : _a6.call(me);
      if (me._hasFeatureTransactionCapture) {
        me.releaseFeatureTransaction();
      }
      me.cleanupTransactionMetadata();
      stm.isRecording && stm.rejectTransaction();
      if (!me.hasStmCapture && me._stmInitiallyDisabled != null) {
        stm.disabled = me._stmInitiallyDisabled;
        stm.autoRecord = me._stmInitiallyAutoRecord;
      }
      me.client.trigger("featureTransactionReject", { id: id2 });
      me.client.trigger("featureTransactionComplete", { id: id2 });
    }
    cleanupTransactionMetadata() {
      this._editorPromiseResolve = null;
    }
    async finishFeatureTransaction() {
      var _a6;
      const me = this, {
        client,
        _stmInitiallyDisabled,
        _stmInitiallyAutoRecord
      } = me, {
        project,
        featureTransactionId: id2
      } = client, { stm } = project;
      if (!id2 || me._hasFeatureTransactionCapture) {
        return;
      }
      client.trigger("featureTransactionFinalizeStart", { id: id2 });
      me.finishingFeatureTransaction = true;
      await project.commitAsync();
      if (stm.isRecording) {
        stm.stopTransaction();
      }
      (_a6 = me._editorPromiseResolve) == null ? void 0 : _a6.call(me);
      me._editorPromiseResolve = null;
      if (!me.hasStmCapture && stm && !stm.isDestroying && _stmInitiallyDisabled != null) {
        stm.disabled = _stmInitiallyDisabled;
        stm.autoRecord = _stmInitiallyAutoRecord;
      }
      if (!me.isDestroying) {
        me.finishingFeatureTransaction = false;
        client.trigger("featureTransactionFinalized", { id: id2 });
        client.trigger("featureTransactionComplete", { id: id2 });
      }
    }
  }, __publicField(_a5, "$name", "TransactionalFeature"), _a5;
};

// ../Scheduler/lib/Scheduler/feature/EventResize.js
var tipAlign = {
  top: "b-t",
  right: "b100-t100",
  bottom: "t-b",
  left: "b0-t0"
};
var EventResize = class extends InstancePlugin.mixin(Draggable_default, Droppable_default, TransactionalFeature_default) {
  static get configurable() {
    return {
      draggingItemCls: "b-sch-event-wrap-resizing",
      resizingItemInnerCls: "b-sch-event-resizing",
      /**
       * Use left handle when resizing. Only applies when owning client's `direction` is 'horizontal'
       * @config {Boolean}
       * @default
       */
      leftHandle: true,
      /**
       * Use right handle when resizing. Only applies when owning client's `direction` is 'horizontal'
       * @config {Boolean}
       * @default
       */
      rightHandle: true,
      /**
       * Use top handle when resizing. Only applies when owning client's direction` is 'vertical'
       * @config {Boolean}
       * @default
       */
      topHandle: true,
      /**
       * Use bottom handle when resizing. Only applies when owning client's `direction` is 'vertical'
       * @config {Boolean}
       * @default
       */
      bottomHandle: true,
      /**
       * Automatically shrink virtual handles when available space < handleSize. The virtual handles will
       * decrease towards width/height 1, reserving space between opposite handles to for example leave room for
       * dragging. To configure reserved space, see {@link #config-reservedSpace}.
       * @config {Boolean}
       * @default false
       */
      dynamicHandleSize: true,
      /**
       * Set to `true` to allow resizing to a zero-duration span
       * @config {Boolean}
       * @default false
       */
      allowResizeToZero: null,
      /**
       * Room in px to leave unoccupied by handles when shrinking them dynamically (see
       * {@link #config-dynamicHandleSize}).
       * @config {Number}
       * @default
       */
      reservedSpace: 1,
      /**
       * The amount of pixels to move pointer/mouse before it counts as a drag operation.
       * @config {Number}
       * @default
       */
      dragThreshold: 0,
      /**
       * The amount of time (ms) to delay a touch-resize interaction.
       * @config {Number}
       * @default
       */
      dragTouchStartDelay: 300,
      draggingClsSelector: ".b-timeline-base",
      /**
       * `false` to not show a tooltip while resizing
       * @config {Boolean}
       * @default
       */
      showTooltip: true,
      /**
       * true to see exact event length during resizing
       * @config {Boolean}
       * @default
       */
      showExactResizePosition: false,
      /**
       * An empty function by default, but provided so that you can perform custom validation on
       * the item being resized. Return true if the new duration is valid, false to signal that it is not.
       * @param {Object} context The resize context, contains the record & dates.
       * @param {Scheduler.model.TimeSpan} context.record The record being resized.
       * @param {Date} context.startDate The new start date.
       * @param {Date} context.endDate The new start date.
       * @param {Date} context.originalStartDate Start date before resize
       * @param {Date} context.originalEndDate End date before resize
       * @param {Event} event The browser Event object
       * @returns {Boolean}
       * @config {Function}
       */
      validatorFn: () => true,
      /**
       * `this` reference for the validatorFn
       * @config {Object}
       */
      validatorFnThisObj: null,
      /**
       * Setting this property may change the configuration of the {@link #config-tip}, or
       * cause it to be destroyed if `null` is passed.
       *
       * Reading this property returns the Tooltip instance.
       * @member {Core.widget.Tooltip|TooltipConfig} tip
       */
      /**
       * If a tooltip is required to illustrate the resize, specify this as `true`, or a config
       * object for the {@link Core.widget.Tooltip}.
       * @config {Core.widget.Tooltip|TooltipConfig}
       */
      tip: {
        $config: ["lazy", "nullify"],
        value: {
          autoShow: false,
          axisLock: true,
          trackMouse: false,
          updateContentOnMouseMove: true,
          hideDelay: 0
        }
      },
      /**
       * A template function returning the content to show during a resize operation.
       *
       * @config {Function} tooltipTemplate
       * @param {Object} context A context object
       * @param {Date} context.startDate New start date
       * @param {Date} context.endDate New end date
       * @param {Scheduler.model.TimeSpan} context.record The record being resized
       * @param {String} context.startClockHtml Predefined HTML to show the start time
       * @param {String} context.endClockHtml Predefined HTML to show the end time
       * @returns {String} String representing the HTML markup
       */
      tooltipTemplate: (context) => `
                <div class="b-sch-tip-${context.valid ? "valid" : "invalid"}">
                    ${context.startClockHtml}
                    ${context.endClockHtml}
                    <div class="b-sch-tip-message">${context.message}</div>
                </div>
            `,
      ignoreSelector: ".b-sch-terminal",
      dragActiveCls: "b-resizing-event",
      /**
       * Locks the layout during drag resize, opt out to use the same rendering pathway for drag resize as for
       * already existing events (updating the layout of all events during the resize operation).
       *
       * Keeping this config enabled also leads to cheaper resizing, only the resized event's resources are
       * refreshed during the operation.
       *
       * For even cheaper resizing, configure it as `'minimal-updates'`. In this mode, only the resized event
       * is refreshed during the operation (not the other events assigned to the same resource).
       *
       * @config {Boolean|'minimal-updates'}
       */
      lockLayout: true,
      /**
       * Set to `false` to not resize all selected events simultaneously.
       *
       * Please note that {@link Scheduler/view/Scheduler#config-multiEventSelect} needs to be enabled for it to
       * work
       *
       * @config {Boolean}
       * @default
       */
      resizeSelected: true
    };
  }
  static get pluginConfig() {
    return {
      chain: ["render", "onEventDataGenerated", "isEventElementDraggable"]
    };
  }
  //endregion
  //region Init & destroy
  doDestroy() {
    var _a5;
    super.doDestroy();
    (_a5 = this.dragging) == null ? void 0 : _a5.destroy();
  }
  render() {
    const me = this, { client } = me;
    me.dragSelector = me.dragItemSelector = client.eventSelector;
    me.dragRootElement = me.dropRootElement = client.timeAxisSubGridElement;
    me.dragLock = client.isVertical ? "y" : "x";
  }
  // Prevent event dragging when it happens over a resize handle
  isEventElementDraggable(eventElement, eventRecord, el, event) {
    const me = this, eventResizable = eventRecord == null ? void 0 : eventRecord.resizable;
    if (me.disabled || !eventResizable || eventRecord.isMilestone) {
      return true;
    }
    return (eventResizable !== true && eventResizable !== "start" || !me.isOverStartHandle(event, eventElement)) && (eventResizable !== true && eventResizable !== "end" || !me.isOverEndHandle(event, eventElement));
  }
  // Called for each event during render, allows manipulation of render data.
  onEventDataGenerated({ eventRecord, wrapperCls, cls }) {
    var _a5, _b2;
    if ((_b2 = (_a5 = this.dragging) == null ? void 0 : _a5.context) == null ? void 0 : _b2.eventContexts.some((ctx) => ctx.eventRecord === eventRecord)) {
      wrapperCls["b-active"] = wrapperCls[this.draggingItemCls] = wrapperCls["b-over-resize-handle"] = cls["b-resize-handle"] = cls[this.resizingItemInnerCls] = 1;
    }
  }
  // Sneak a first peek at the drag event to put necessary date values into the context
  onDragPointerMove(event) {
    var _a5;
    const {
      client,
      dragging
    } = this, {
      visibleDateRange,
      isHorizontal
    } = client, rtl = isHorizontal && client.rtl, dimension = isHorizontal ? "X" : "Y", pageScroll = globalThis[`page${dimension}Offset`], coord = event[`page${dimension}`] + (((_a5 = dragging.context) == null ? void 0 : _a5.offset) || 0), clientRect = Rectangle.from(client.timeAxisSubGridElement, null, true), startCoord = clientRect.getStart(rtl, isHorizontal), endCoord = clientRect.getEnd(rtl, isHorizontal);
    let date2 = client.getDateFromCoord({ coord, local: false });
    if (rtl) {
      if (coord - pageScroll > startCoord) {
        date2 = visibleDateRange.startDate;
      } else if (coord < endCoord) {
        date2 = visibleDateRange.endDate;
      }
    } else if (coord - pageScroll < startCoord) {
      date2 = visibleDateRange.startDate;
    } else if (coord - pageScroll > endCoord) {
      date2 = visibleDateRange.endDate;
    }
    dragging.clientStartCoord = startCoord;
    dragging.clientEndCoord = endCoord;
    dragging.date = date2;
    super.onDragPointerMove(event);
  }
  /**
   * Returns true if a resize operation is active
   * @property {Boolean}
   * @readonly
   */
  get isResizing() {
    return Boolean(this.dragging);
  }
  /**
   * Returns true if resizing multiple events
   * @private
   * @property {Boolean}
   * @readonly
   */
  get isMultiResizing() {
    var _a5;
    return this.isResizing && ((_a5 = this.dragging) == null ? void 0 : _a5.context.eventContexts.length) > 1;
  }
  beforeDrag(drag) {
    const me = this, { client } = me, eventRecord = client.resolveTimeSpanRecord(drag.itemElement), resourceRecord = !client.isGanttBase && client.resolveResourceRecord(client.isVertical ? drag.startEvent : drag.itemElement);
    if (me.disabled || client.readOnly || (resourceRecord == null ? void 0 : resourceRecord.readOnly) || eventRecord && (eventRecord.readOnly || !(eventRecord.project || eventRecord.isOccurrence)) || super.beforeDrag(drag) === false) {
      return false;
    }
    if (!me.hasStmCapture) {
      if (!me.canStartFeatureTransaction) {
        client.trigger("eventResizePrevented", { reason: "revision" });
        return false;
      }
    }
    drag.mousedownDate = drag.date = client.getDateFromCoordinate(drag.event[`page${client.isHorizontal ? "X" : "Y"}`], null, false);
    return me.triggerBeforeResize(drag);
  }
  initEventRecordContext(record, mainContext) {
    const element = record === mainContext.timespanRecord ? mainContext.element : this.getSelectedRecordElement(record), eventRecord = record.isAssignmentModel ? record.event : record, {
      isBatchUpdating,
      // For EventBuffer feature
      wrapEndDate,
      wrapStartDate
    } = eventRecord, startDate = isBatchUpdating ? eventRecord.get("startDate") : eventRecord.startDate, endDate = isBatchUpdating ? eventRecord.get("endDate") : eventRecord.endDate, date2 = mainContext.draggingEnd ? endDate : startDate;
    return {
      mainContext,
      eventRecord,
      element,
      startDate,
      endDate,
      date: date2,
      wrapEndDate,
      wrapStartDate,
      oldValue: date2,
      originalStartDate: startDate,
      originalEndDate: endDate,
      timespanRecord: eventRecord
    };
  }
  initResizeContext(drag) {
    var _a5, _b2, _c2;
    const me = this, { client } = me, {
      startEvent,
      itemElement
    } = drag, eventRecord = client.resolveEventRecord(itemElement), endDate = eventRecord.endDate, startDate = eventRecord.startDate, useEventBuffer = (_a5 = client.features.eventBuffer) == null ? void 0 : _a5.enabled, horizontal = me.dragLock === "x", rtl = horizontal && client.rtl, draggingEnd = me.isOverEndHandle(startEvent, itemElement), elRect = Rectangle.from(itemElement), startCoord = horizontal ? startEvent.clientX : startEvent.clientY, endCoord = draggingEnd ? elRect.getEnd(rtl, horizontal) : elRect.getStart(rtl, horizontal), oldValue = draggingEnd ? endDate : startDate, context = {
      owner: me,
      valid: true,
      offset: useEventBuffer ? 0 : endCoord - startCoord,
      edge: horizontal ? draggingEnd ? "right" : "left" : draggingEnd ? "bottom" : "top",
      finalize: me.finalize,
      event: drag.event,
      toSet: draggingEnd ? "endDate" : "startDate",
      otherEnd: draggingEnd ? "startDate" : "endDate",
      setMethod: draggingEnd ? "setEndDate" : "setStartDate",
      setOtherMethod: draggingEnd ? "setStartDate" : "setEndDate",
      wrapToSet: !useEventBuffer ? null : draggingEnd ? "wrapEndDate" : "wrapStartDate",
      oldValue,
      draggingEnd,
      // For Nested Events feature
      timespanRecord: eventRecord,
      // EventRecord for products
      [`${client.scheduledEventName}Record`]: eventRecord,
      // For backwards compatibility (mostly tests)
      element: ((_c2 = (_b2 = this.eventContexts) == null ? void 0 : _b2[0]) == null ? void 0 : _c2.element) || itemElement
    };
    let events;
    if (me.resizeSelected && me.isRecordSelected(eventRecord)) {
      events = me.selectedRecords.filter((r) => r.isAssignmentModel ? !r.event.readOnly && r.event.isScheduled : !r.readOnly && r.isScheduled);
      events.sort((a, b) => a === eventRecord ? -1 : b === eventRecord ? 1 : 0);
    } else {
      events = [eventRecord];
    }
    context.eventContexts = events.map((event) => me.initEventRecordContext(event, context));
    return context;
  }
  get selectedRecords() {
    return this.client.selectedAssignments;
  }
  isRecordSelected(record) {
    var _a5, _b2;
    return (_b2 = (_a5 = this.client).isEventSelected) == null ? void 0 : _b2.call(_a5, record);
  }
  getSelectedRecordElement(record) {
    return record.isAssignmentModel ? this.client.getElementFromAssignmentRecord(record, false, false, this.client.isLockedRowsView) : this.client.getElementFromEventRecord(record);
  }
  startDrag(...params) {
    const result = super.startDrag(...params);
    if (result !== false && !this.hasStmCapture) {
      this.startFeatureTransaction().then();
    }
    return result;
  }
  dragStart(drag) {
    const me = this, { client, tip } = me, context = drag.context = me.initResizeContext(drag);
    client.element.classList.add(...me.dragActiveCls.split(" "));
    if (!client.listenToBatchedUpdates) {
      client.beginListeningForBatchedUpdates();
    }
    for (const ctx of context.eventContexts) {
      const { eventRecord } = ctx;
      eventRecord.meta.isResizing = true;
      if (!eventRecord.isBatchUpdating) {
        me.beginEventRecordBatch(eventRecord);
      }
      if (ctx.element) {
        me.setupProductResizeContext(ctx, drag.startEvent);
      }
    }
    me.triggerEventResizeStart(`${client.scheduledEventName}ResizeStart`, me.getTriggerParams(context), context);
    if (tip) {
      tip.show();
      tip.align = tipAlign[context.edge];
      tip.showBy(me.getTooltipTarget(drag));
    }
  }
  getTriggerParams(context) {
    const resizeData = context.eventContexts.map((eventContext) => ({ ...eventContext })), eventName = this.client.capitalizedEventName.toLowerCase(), data = resizeData.find(({
      element,
      mainContext
    }) => {
      var _a5;
      return (_a5 = element == null ? void 0 : element.closest(`.b-sch-${eventName}-wrap`)) == null ? void 0 : _a5.isEqualNode(mainContext.element);
    }) || resizeData[0], result = {
      ...data,
      event: context.event,
      async: context.async,
      finalize: (...args) => context.finalize(...args),
      resizeData
    };
    delete result.mainContext;
    return result;
  }
  // Subclasses may override this
  triggerBeforeResize(drag) {
    const { client } = this, eventRecord = client.resolveTimeSpanRecord(drag.itemElement);
    return client.trigger(
      `before${client.capitalizedEventName}Resize`,
      {
        [`${client.scheduledEventName}Record`]: eventRecord,
        event: drag.event,
        ...this.getBeforeResizeParams({ event: drag.startEvent, element: drag.itemElement })
      }
    );
  }
  // Subclasses may override this
  triggerEventResizeStart(eventType, event, context) {
    var _a5, _b2;
    this.client.trigger(eventType, event);
    (_b2 = (_a5 = this.client)[`after${StringHelper.capitalize(eventType)}`]) == null ? void 0 : _b2.call(_a5, context, event);
  }
  triggerEventResizeEnd(eventType, event) {
    this.client.trigger(eventType, event);
  }
  triggerEventPartialResize(eventType, resizeData) {
    const name = this.client.scheduledEventName + "Record", batching = /* @__PURE__ */ new Map();
    for (const params of resizeData) {
      const record = params[name];
      batching.set(record, record.batching);
      record.batching = 0;
    }
    this.client.trigger(eventType, {
      ...resizeData[0],
      resizeData
    });
    for (const params of resizeData) {
      const record = params[name];
      record.batching = batching.get(record);
    }
  }
  async triggerBeforeEventResizeFinalize(eventType, event) {
    await this.client.trigger(eventType, event);
  }
  dragEnter(drag) {
    var _a5;
    return ((_a5 = drag.context) == null ? void 0 : _a5.owner) === this;
  }
  resizeEventPartiallyInternal(eventRecord, context) {
    var _a5;
    const { client } = this, { toSet } = context.mainContext;
    if ((_a5 = client.features.eventBuffer) == null ? void 0 : _a5.enabled) {
      if (toSet === "startDate") {
        const diff = context.startDate.getTime() - context.originalStartDate.getTime();
        eventRecord.wrapStartDate = new Date(context.wrapStartDate.getTime() + diff);
      } else if (toSet === "endDate") {
        const diff = context.endDate.getTime() - context.originalEndDate.getTime();
        eventRecord.wrapEndDate = new Date(context.wrapEndDate.getTime() + diff);
      }
    }
    eventRecord.set(toSet, context[toSet]);
  }
  applyDateConstraints(date2, eventRecord, context) {
    var _a5, _b2;
    const minDate = (_a5 = context.dateConstraints) == null ? void 0 : _a5.start, maxDate = (_b2 = context.dateConstraints) == null ? void 0 : _b2.end;
    if (minDate || maxDate) {
      date2 = DateHelper.constrain(date2, minDate, maxDate);
      context.snappedDate = DateHelper.constrain(context.snappedDate, minDate, maxDate);
    }
    return date2;
  }
  isDateRangeValid(context) {
    var _a5;
    const { client } = this, { startDate, endDate, eventRecord } = context, bufferIsUnavailableTime = (_a5 = client.features.eventBuffer) == null ? void 0 : _a5.bufferIsUnavailableTime;
    return client.isDateRangeAvailable(
      eventRecord.preamble && bufferIsUnavailableTime ? DateHelper.add(startDate, -eventRecord.preamble.milliseconds, "ms") : startDate,
      eventRecord.postamble && bufferIsUnavailableTime ? DateHelper.add(endDate, eventRecord.postamble.milliseconds, "ms") : endDate,
      eventRecord,
      context.eventRecord.resources
    );
  }
  /**
   * Validates that the resized event has a valid size based on configuration.
   * Returns true if zero-size events are allowed or if snapping is enabled.
   * Otherwise checks that the event duration is non-zero.
   * @private
   */
  hasValidSize(context, toSet) {
    const { client } = this, oppositeEnd = toSet === "startDate" ? "endDate" : "startDate";
    if (client.timeAxisViewModel.snap || this.allowResizeToZero) {
      return true;
    }
    return context[toSet] - context[oppositeEnd] !== 0;
  }
  resizeEventRecord(context, drag, date2) {
    const me = this, { client } = me, {
      timeAxis,
      enableEventAnimations
    } = client, {
      eventRecord,
      oldValue,
      element
    } = context, {
      resolutionUnit,
      resolutionIncrement
    } = timeAxis, startDate = eventRecord.get("startDate"), endDate = eventRecord.get("endDate"), dragContext = context.mainContext;
    let {
      toSet,
      otherEnd
    } = dragContext, crossedOver, avoidedZeroSize;
    if (toSet === "endDate") {
      if (date2 < startDate) {
        crossedOver = -1;
      }
    } else {
      if (date2 > endDate) {
        crossedOver = 1;
      }
    }
    if (crossedOver && me.onDragEndSwitch) {
      me.onDragEndSwitch(context, date2, crossedOver);
      otherEnd = dragContext.otherEnd;
      toSet = dragContext.toSet;
    }
    if (client.snapRelativeToEventStartDate) {
      date2 = timeAxis.roundDate(date2, oldValue);
    }
    context.snappedDate = DateHelper.round(date2, timeAxis.resolution, null, timeAxis.weekStartDay);
    const duration = DateHelper.diff(date2, context[otherEnd], resolutionUnit) * (dragContext.draggingEnd ? -1 : 1);
    if (me.isEventDragCreate && context.eventRecord === context.mainContext.eventRecord) {
      context.mainContext.tooNarrow = Math.abs(duration) < resolutionIncrement / 2;
    } else if (duration < resolutionIncrement) {
      if (duration < 0) {
        return;
      }
      if (me.allowResizeToZero) {
        context.snappedDate = date2 = context[otherEnd];
      } else {
        const sign = otherEnd === "startDate" ? 1 : -1;
        let snappedDate = timeAxis.ceilDate(DateHelper.add(eventRecord.get(otherEnd), resolutionIncrement * sign, resolutionUnit));
        const newDuration = DateHelper.diff(snappedDate, context[otherEnd], resolutionUnit) * (dragContext.draggingEnd ? -1 : 1);
        if (newDuration > resolutionIncrement) {
          snappedDate = timeAxis.roundDate(DateHelper.add(snappedDate, -sign, resolutionUnit));
        }
        if ((snappedDate - oldValue) * sign < 0 || (date2 - oldValue) * sign > 0) {
          date2 = sign > 0 ? DateHelper.max(date2, snappedDate) : DateHelper.min(date2, snappedDate);
          context.snappedDate = snappedDate;
        } else {
          date2 = sign > 0 ? DateHelper.max(date2, oldValue) : DateHelper.min(date2, oldValue);
          context.snappedDate = oldValue;
        }
        avoidedZeroSize = true;
      }
    }
    date2 = me.applyDateConstraints(date2, eventRecord, context);
    if (!context.date || date2 - context.date || avoidedZeroSize) {
      context.date = date2;
      context[toSet] = me.showExactResizePosition || client.timeAxisViewModel.snap ? context.snappedDate : date2;
      context.valid = me.isDateRangeValid(context) && me.hasValidSize(context, toSet);
      if (eventRecord.get(toSet) - context[toSet]) {
        context.valid = me.checkValidity(context, drag.event, toSet);
        context.message = "";
        if (context.valid && typeof context.valid !== "boolean") {
          context.message = context.valid.message;
          context.valid = context.valid.valid;
        }
        context.valid = context.valid !== false;
        if (eventRecord.isOccurrence) {
          eventRecord.stores = [client.eventStore];
        }
        client.enableEventAnimations = false;
        this.resizeEventPartiallyInternal(eventRecord, context);
        client.enableEventAnimations = enableEventAnimations;
        if (eventRecord.isOccurrence) {
          eventRecord.stores = null;
        }
        if (context.tooNarrow) {
          context.valid = false;
        }
        return {
          [client.scheduledEventName + "Record"]: eventRecord,
          startDate,
          endDate,
          element,
          ...{ [toSet]: context[toSet] }
        };
      }
    }
  }
  moveDrag(drag) {
    var _a5;
    const me = this, { context, event } = drag, { client, tip } = me, horizontal = me.dragLock === "x", dimension = horizontal ? "X" : "Y", { visibleDateRange } = client, rtl = horizontal && client.rtl, offset = context.offset * (rtl ? -1 : 1), coord = event[`client${dimension}`] + offset, clientRect = Rectangle.from(client.timeAxisSubGridElement, null, true), startCoord = clientRect.getStart(rtl, horizontal), endCoord = clientRect.getEnd(rtl, horizontal), resizeData = [];
    let { date: date2 } = drag;
    context.event = event;
    if (event.isScroll) {
      drag.date = client.getDateFromCoordinate(event[`page${dimension}`] + offset, null, false);
    }
    if (rtl) {
      if (coord > startCoord) {
        date2 = drag.date = visibleDateRange.startDate;
      } else if (coord < endCoord) {
        date2 = drag.date = visibleDateRange.endDate;
      }
    } else if (coord < startCoord) {
      date2 = drag.date = visibleDateRange.startDate;
    } else if (coord > endCoord) {
      date2 = drag.date = visibleDateRange.endDate;
    }
    context.message = "";
    context.valid = true;
    let deltaMS = date2 - context.oldValue;
    if (me.client.capitalizedEventName === "Task") {
      deltaMS = date2 - context.eventContexts[0].oldValue;
    }
    for (const ctx of context.eventContexts) {
      const eventDate = new Date(ctx.oldValue.getTime() + deltaMS), recordResizeData = me.resizeEventRecord(ctx, drag, eventDate);
      if (recordResizeData) {
        resizeData.push(recordResizeData);
      }
      if (!ctx.valid) {
        context.valid = false;
      }
      if ((_a5 = ctx.message) == null ? void 0 : _a5.length) {
        context.message = ctx.message;
      }
    }
    if (tip) {
      tip.align = tipAlign[context.edge];
      tip.alignTo(me.getTooltipTarget(drag));
    }
    if (resizeData.length) {
      me.triggerEventPartialResize(`${me.client.scheduledEventName}PartialResize`, resizeData);
    }
    super.moveDrag(drag);
  }
  dragEnd(drag) {
    const { context } = drag;
    if (context) {
      context.event = drag.event;
    }
    if (drag.aborted) {
      context == null ? void 0 : context.finalize(false);
    } else if (!this.isEventDragCreate && !drag.started && !EventHelper.getPagePoint(drag.event).equals(EventHelper.getPagePoint(drag.startEvent))) {
      this.dragStart(drag);
      this.cleanup(drag.context, false);
    }
  }
  async dragDrop({ context, event }) {
    var _a5;
    const me = this, { client } = me, { toSet } = context;
    let valid = true, modified;
    (_a5 = me.tip) == null ? void 0 : _a5.hide();
    for (const eventContext of context.eventContexts) {
      eventContext[toSet] = eventContext.snappedDate;
      const { startDate, endDate } = eventContext;
      eventContext.valid = startDate && endDate && (me.allowResizeToZero || endDate - startDate > 0) && // Make sure dragged end changed
      eventContext[toSet] - eventContext.oldValue && eventContext.valid !== false;
      if (!eventContext.valid) {
        valid = false;
      }
    }
    if (valid) {
      const params = me.getTriggerParams(context), event2 = {
        ...params,
        context: params
      };
      await me.triggerBeforeEventResizeFinalize(`before${client.capitalizedEventName}ResizeFinalize`, event2);
      if (event2.async || params.async) {
        context.async = true;
      }
      modified = true;
    }
    if (!context.async) {
      await context.finalize(modified);
    }
  }
  async eventRecordFinalize(context, updateRecord) {
    const me = this, { client } = me;
    let updatePromises = [], changed = false;
    if (updateRecord) {
      updatePromises = context.eventContexts.map((ctx) => me.internalUpdateRecord(ctx, ctx.eventRecord));
    } else {
      for (const ctx of context.eventContexts) {
        const { eventRecord } = ctx;
        me.cancelEventRecordBatch(eventRecord);
        if (eventRecord.isOccurrence) {
          eventRecord.resources.forEach((resource) => client.repaintEventsForResource(resource));
        }
      }
    }
    if (updatePromises.length) {
      await Promise.all(updatePromises);
      if (me.isDestroyed) {
        return;
      }
      changed = updatePromises.some((b) => b);
    }
    me.cleanup(context, changed);
  }
  // This is called with a thisObj of the context object
  // We set "me" to the owner, and "context" to the thisObj so that it
  // reads as if it were a method of this class.
  async finalize(updateRecord) {
    const me = this.owner, context = this, {
      snapRelativeToEventStartDate,
      timeAxis
    } = me.client, { toSet } = context;
    if (context.finalizing) {
      return;
    }
    context.finalizing = true;
    if (updateRecord) {
      context.eventContexts.forEach((ctx) => {
        const { oldValue } = ctx;
        if (snapRelativeToEventStartDate) {
          ctx[toSet] = ctx.snappedDate = timeAxis.roundDate(ctx.date, oldValue);
        }
        me.adjustRecordBeforeUpdating(ctx, ctx.eventRecord);
      });
      me.client.endListeningForBatchedUpdates();
    }
    await me.eventRecordFinalize(context, updateRecord);
    if (updateRecord) {
      await me.finishFeatureTransaction();
    } else {
      me.rejectFeatureTransaction();
    }
  }
  // This is always called on drop or abort.
  cleanup(context, changed) {
    var _a5;
    const me = this, { client } = me;
    for (const { element, eventRecord } of context.eventContexts) {
      eventRecord.meta.isResizing = false;
      me.unHighlightHandle(element);
    }
    client.endListeningForBatchedUpdates();
    (_a5 = me.tip) == null ? void 0 : _a5.hide();
    client.element.classList.remove(...me.dragActiveCls.split(" "));
    me.triggerEventResizeEnd(`${client.scheduledEventName}ResizeEnd`, { ...me.getTriggerParams(context), changed });
  }
  // It does the last adjustment event record adjustment.
  // Called before we disable batch changes tracking.
  adjustRecordBeforeUpdating(context, timespanRecord) {
    const { startDate, endDate } = context, { toSet, draggingEnd, otherEnd } = context.mainContext;
    if (!timespanRecord.isOccurrence && timespanRecord.isEntity && (!timespanRecord.manuallyScheduled || timespanRecord.project.skipNonWorkingTimeWhenSchedulingManually)) {
      const duration = timespanRecord.run("calculateProjectedDuration", startDate, endDate);
      timespanRecord.set({
        // Fix the dragged date point according to the Entity's rules.
        [toSet]: timespanRecord.run(
          "calculateProjectedXDateWithDuration",
          draggingEnd ? startDate : endDate,
          draggingEnd,
          duration
        ),
        [otherEnd]: context[otherEnd]
      });
    }
  }
  async internalUpdateRecord(context, timespanRecord) {
    var _a5;
    const { client } = this, { generation } = timespanRecord, {
      startDate,
      endDate
    } = context, {
      toSet,
      wrapToSet,
      setMethod
    } = context.mainContext;
    if (timespanRecord.isOccurrence) {
      timespanRecord[timespanRecord.batching > 1 ? "endBatch" : "cancelBatch"]();
      timespanRecord.set(TimeSpan.prototype.inSetNormalize.call(timespanRecord, { startDate, endDate }));
    } else {
      const batchChanges = Object.assign({}, timespanRecord.meta.batchChanges);
      delete batchChanges[toSet];
      delete batchChanges.duration;
      this.cancelEventRecordBatch(timespanRecord);
      if ((_a5 = client.features.eventBuffer) == null ? void 0 : _a5.enabled) {
        timespanRecord[wrapToSet] = null;
      }
      timespanRecord[setMethod](context[toSet], false);
      if (Object.keys(batchChanges).length) {
        timespanRecord.set(batchChanges);
      }
    }
    await client.project.commitAsync();
    return timespanRecord.generation !== generation;
  }
  onDragItemMouseMove(event) {
    if (event.pointerType !== "touch" && !this.handleSelector && event.buttons === 0) {
      this.checkResizeHandles(event);
    }
  }
  /**
   * Check if mouse is over a resize handle (virtual). If so, highlight.
   * @private
   * @param {MouseEvent} event
   */
  checkResizeHandles(event) {
    const me = this, { overItem } = me;
    if (overItem && !me.client.readOnly && (!me.allowResize || me.allowResize(overItem, event))) {
      const eventRecord = me.client.resolveTimeSpanRecord(overItem);
      if (eventRecord == null ? void 0 : eventRecord.readOnly) {
        return;
      }
      if (me.isOverAnyHandle(event, overItem)) {
        me.highlightHandle();
      } else {
        me.unHighlightHandle();
      }
    }
  }
  onDragItemMouseLeave(event, oldOverItem) {
    this.unHighlightHandle(oldOverItem);
  }
  /**
   * Highlights handles (applies css that changes cursor).
   * @private
   */
  highlightHandle() {
    var _a5, _b2;
    const {
      overItem: item,
      client
    } = this, handleTargetElement = (_b2 = (_a5 = item.syncIdMap) == null ? void 0 : _a5[client.scheduledEventName]) != null ? _b2 : item.querySelector(client.eventInnerSelector);
    handleTargetElement.classList.add("b-resize-handle");
    item.classList.add("b-over-resize-handle");
  }
  /**
   * Unhighlight handles (removes css).
   * @private
   */
  unHighlightHandle(item = this.overItem) {
    var _a5, _b2;
    if (item) {
      const me = this, inner = (_b2 = (_a5 = item.syncIdMap) == null ? void 0 : _a5[me.client.scheduledEventName]) != null ? _b2 : item.querySelector(me.client.eventInnerSelector);
      if (inner) {
        inner.classList.remove("b-resize-handle", me.resizingItemInnerCls);
      }
      item.classList.remove("b-over-resize-handle", me.draggingItemCls);
    }
  }
  isOverAnyHandle(event, target) {
    return Boolean(this.isOverStartHandle(event, target) || this.isOverEndHandle(event, target));
  }
  isOverStartHandle(event, target) {
    var _a5;
    return Boolean((_a5 = this.getHandleRect("start", event, target)) == null ? void 0 : _a5.contains(EventHelper.getPagePoint(event)));
  }
  isOverEndHandle(event, target) {
    var _a5;
    return Boolean((_a5 = this.getHandleRect("end", event, target)) == null ? void 0 : _a5.contains(EventHelper.getPagePoint(event)));
  }
  getHandleRect(side, event, eventEl) {
    if (this.overItem) {
      eventEl = event.target.closest(`.${this.client.eventCls}`) || eventEl.querySelector(`.${this.client.eventCls}`);
      if (!eventEl) {
        return;
      }
      const me = this, start = side === "start", { client } = me, rtl = Boolean(client.rtl), axis = me.dragLock, horizontal = axis === "x", dim = horizontal ? "width" : "height", handleSpec = `${horizontal ? start ? rtl ? "right" : "left" : rtl ? "left" : "right" : start ? "top" : "bottom"}Handle`, timespanRecord = client.resolveTimeSpanRecord(eventEl), resizable = timespanRecord == null ? void 0 : timespanRecord.isResizable, eventRect = Rectangle.from(eventEl), result = eventRect.clone(), handleStyle = globalThis.getComputedStyle(eventEl, ":before"), centerGap = me.dynamicHandleSize ? me.reservedSpace / 2 : 0, deflateArgs = [0, 0, 0, 0];
      let handleSize = parseFloat(handleStyle[dim]);
      if (!me.disabled && handleSize && !isNaN(handleSize) && me[handleSpec] && me.dynamicHandleSize && (resizable === true || resizable === side)) {
        const oppositeEnd = !horizontal && !start || horizontal && rtl === start, eventSize = eventRect[dim];
        if (handleSize > 0.5 * eventSize) {
          handleSize = Math.max(Math.floor(eventSize / 4) - 1, 1);
        }
        if (oppositeEnd) {
          result[axis] += eventSize - handleSize;
          deflateArgs[horizontal ? 3 : 0] = eventSize / 2 + centerGap;
        } else {
          deflateArgs[horizontal ? 1 : 2] = eventSize / 2 + centerGap;
        }
        eventRect.deflate(...deflateArgs);
        result[dim] = handleSize;
        result.constrainTo(eventRect);
        if (result[dim]) {
          return result;
        }
      }
    }
  }
  setupDragContext(event) {
    const me = this;
    if (me.overItem && me.isOverAnyHandle(event, me.overItem) && me.isElementResizable(me.overItem, event)) {
      const result = super.setupDragContext(event);
      result.scrollManager = me.client.scrollManager;
      return result;
    }
  }
  changeTip(tip, oldTip) {
    var _a5;
    const me = this;
    if (!me.showTooltip) {
      return null;
    }
    if (tip) {
      if (tip.isTooltip) {
        tip.owner = me;
      } else {
        tip = Tooltip.reconfigure(oldTip, Tooltip.mergeConfigs({
          id: me.tipId
        }, tip, {
          getHtml: me.getTipHtml.bind(me),
          owner: me.client
        }, me.tip), {
          owner: me,
          defaults: {
            type: "tooltip"
          }
        });
      }
      tip.ion({
        innerhtmlupdate: "updateDateIndicator",
        thisObj: me
      });
      me.clockTemplate = new ClockTemplate({
        scheduler: me.client
      });
    } else if (oldTip) {
      oldTip.destroy();
      (_a5 = me.clockTemplate) == null ? void 0 : _a5.destroy();
    }
    return tip;
  }
  //endregion
  //region Events
  isElementResizable(element, event) {
    var _a5;
    const me = this, { client } = me, timespanRecord = client.resolveTimeSpanRecord(element);
    if (client.readOnly) {
      return false;
    }
    let resizable = timespanRecord == null ? void 0 : timespanRecord.isResizable;
    const handleHoldingElement = (_a5 = element == null ? void 0 : element.syncIdMap[client.scheduledEventName]) != null ? _a5 : element, handleEl = event.target.closest('[class$="-handle"]');
    if (!resizable || handleEl && handleEl !== handleHoldingElement) {
      return false;
    }
    element = event.target.closest(me.dragSelector);
    if (!element) {
      return false;
    }
    const startsOutside = element.classList.contains("b-sch-event-startsoutside"), endsOutside = element.classList.contains("b-sch-event-endsoutside");
    if (resizable === true) {
      if (startsOutside && endsOutside) {
        return false;
      } else if (startsOutside) {
        resizable = "end";
      } else if (endsOutside) {
        resizable = "start";
      } else {
        return me.isOverStartHandle(event, element) || me.isOverEndHandle(event, element);
      }
    }
    if (startsOutside && resizable === "start" || endsOutside && resizable === "end") {
      return false;
    }
    if (me.isOverStartHandle(event, element) && resizable === "start" || me.isOverEndHandle(event, element) && resizable === "end") {
      return true;
    }
    return false;
  }
  updateDateIndicator() {
    const { clockTemplate } = this, { context } = this.dragging, {
      eventRecord,
      snappedDate
    } = context.eventContexts[0], { draggingEnd } = context, startDate = draggingEnd ? eventRecord.get("startDate") : snappedDate, endDate = draggingEnd ? snappedDate : eventRecord.get("endDate"), { element } = this.tip;
    clockTemplate.updateDateIndicator(element.querySelector(".b-sch-tooltip-startdate"), startDate);
    clockTemplate.updateDateIndicator(element.querySelector(".b-sch-tooltip-enddate"), endDate);
  }
  getTooltipTarget({ itemElement, context }) {
    const me = this, { rtl } = me.client, target = Rectangle.from(itemElement, null, true);
    if (me.dragLock === "x") {
      if (!rtl && context.edge === "right" || rtl && context.edge === "left") {
        target.x = target.right - 1;
      } else {
        target.x -= me.tip.anchorSize[0] / 2;
      }
      target.width = me.tip.anchorSize[0] / 2;
    } else {
      if (context.edge === "bottom") {
        target.y = target.bottom - 1;
      }
      target.height = me.tip.anchorSize[1] / 2;
    }
    return { target };
  }
  basicValidityCheck(context, event) {
    return context.startDate && (context.endDate > context.startDate || this.allowResizeToZero) && this.validatorFn.call(this.validatorFnThisObj || this, context, event);
  }
  //endregion
  //region Tooltip
  getTipHtml({ tip }) {
    const me = this, { context } = me.dragging, draggingEventContext = context.eventContexts.find((ctx) => ctx.eventRecord === context.timespanRecord), {
      startDate,
      endDate,
      snappedDate,
      eventRecord
    } = draggingEventContext, {
      toSet,
      valid,
      message = ""
    } = context;
    if (!startDate || !endDate) {
      return tip.html;
    }
    const tipData = {
      record: eventRecord,
      valid,
      message,
      startDate,
      endDate,
      [toSet]: snappedDate
    };
    tipData.startText = me.client.getFormattedDate(tipData.startDate);
    tipData.endText = me.client.getFormattedDate(tipData.endDate);
    tipData.startClockHtml = me.clockTemplate.template({
      date: tipData.startDate,
      text: tipData.startText,
      cls: "b-sch-tooltip-startdate"
    });
    tipData.endClockHtml = me.clockTemplate.template({
      date: tipData.endDate,
      text: tipData.endText,
      cls: "b-sch-tooltip-enddate"
    });
    return me.tooltipTemplate(tipData);
  }
  //endregion
  //region Product specific, may be overridden in subclasses
  beginEventRecordBatch(eventRecord) {
    eventRecord.beginBatch();
  }
  cancelEventRecordBatch(eventRecord) {
    eventRecord.cancelBatch();
  }
  getBeforeResizeParams(context) {
    const { client } = this;
    return {
      resourceRecord: client.resolveResourceRecord(client.isVertical ? context.event : context.element)
    };
  }
  setupProductResizeContext(context, event) {
    var _a5, _b2, _c2;
    const { client } = this, { element } = context, eventRecord = client.resolveEventRecord(element), resourceRecord = (_a5 = client.resolveResourceRecord) == null ? void 0 : _a5.call(client, element), assignmentRecord = (_b2 = client.resolveAssignmentRecord) == null ? void 0 : _b2.call(client, element);
    Object.assign(context, {
      eventRecord,
      resourceRecord,
      assignmentRecord,
      dateConstraints: (_c2 = client.getDateConstraints) == null ? void 0 : _c2.call(client, resourceRecord, eventRecord)
    });
  }
  // Helper method to check for event overlap
  checkForEventOverlap({ startDate, endDate, snappedDate, eventRecord, toSet }) {
    var _a5;
    const { client } = this, bufferIsUnavailableTime = (_a5 = client.features.eventBuffer) == null ? void 0 : _a5.bufferIsUnavailableTime, { resources } = eventRecord;
    if (eventRecord.preamble && bufferIsUnavailableTime) {
      if (toSet === "startDate") {
        snappedDate = DateHelper.add(snappedDate, -eventRecord.preamble.milliseconds, "ms");
      } else {
        startDate = DateHelper.add(startDate, -eventRecord.preamble.milliseconds, "ms");
      }
    }
    if (eventRecord.postamble && bufferIsUnavailableTime) {
      if (toSet === "startDate") {
        endDate = DateHelper.add(endDate, eventRecord.postamble.milliseconds, "ms");
      } else {
        snappedDate = DateHelper.add(snappedDate, eventRecord.postamble.milliseconds, "ms");
      }
    }
    if (toSet === "startDate") {
      return !client.isDateRangeAvailable(snappedDate, endDate, eventRecord, resources);
    }
    return !client.isDateRangeAvailable(startDate, snappedDate, eventRecord, resources);
  }
  checkValidity({ startDate, endDate, eventRecord, snappedDate, resourceRecord }, event, toSet) {
    const isOverlapping = this.checkForEventOverlap({ startDate, endDate, snappedDate, eventRecord, toSet });
    if (isOverlapping) {
      return {
        valid: false,
        message: this.L("L{EventDrag.eventOverlapsExisting}")
      };
    }
    return this.basicValidityCheck(...arguments);
  }
  get tipId() {
    return `${this.client.id}-event-resize-tip`;
  }
  //endregion
};
//region Events
/**
 * Fired on the owning Scheduler before resizing starts. Return `false` to prevent the action.
 * @event beforeEventResize
 * @on-owner
 * @preventable
 * @param {Scheduler.view.Scheduler} source Scheduler instance
 * @param {Scheduler.model.EventModel} eventRecord Event record being resized
 * @param {Scheduler.model.ResourceModel} resourceRecord Resource record the resize starts within
 * @param {MouseEvent} event Browser event
 */
/**
 * Fires on the owning Scheduler when event resizing starts
 * @event eventResizeStart
 * @on-owner
 * @param {Scheduler.view.Scheduler} source Scheduler instance
 * @param {Scheduler.model.EventModel} eventRecord Event record being resized
 * @param {Scheduler.model.ResourceModel} resourceRecord Resource record the resize starts within
 * @param {MouseEvent} event Browser event
 * @param {EventResizeData[]} resizeData Resize data for selected events. Only available when {@link #config-resizeSelected} is `true`
 */
/**
 * Fires on the owning Scheduler on each resize move event
 * @event eventPartialResize
 * @on-owner
 * @param {Scheduler.view.Scheduler} source Scheduler instance
 * @param {Scheduler.model.EventModel} eventRecord Event record being resized
 * @param {Date} startDate
 * @param {Date} endDate
 * @param {HTMLElement} element
 * @param {EventResizeData[]} resizeData Resize data for selected events
 */
/**
 * Fired on the owning Scheduler to allow implementer to prevent immediate finalization by returning a promise
 * in the listener, to show a confirmation popup etc
 *
 * ```javascript
 *  scheduler.on('beforeeventresizefinalize', event => {
 *      event.async = true;
 *      setTimeout(() => {
 *          // async code don't forget to call finalize
 *          event.finalize();
 *      }, 1000);
 *  })
 * ```
 *
 * @event beforeEventResizeFinalize
 * @on-owner
 * @async
 * @param {Scheduler.view.Scheduler} source Scheduler instance
 * @param {Object} context [DEPRECATED] de-nested to outer params object
 * @param {Scheduler.model.EventModel} eventRecord Event record being resized
 * @param {Date} startDate New startDate (changed if resizing start side)
 * @param {Date} endDate New endDate (changed if resizing end side)
 * @param {Date} originalStartDate Start date before resize
 * @param {Date} originalEndDate End date before resize
 * @param {Boolean} async Set true to handle resize asynchronously (e.g. to wait for user confirmation)
 * @param {Function} finalize Call this method to finalize resize. This method accepts one argument:
 *                   pass `true` to update records, or `false`, to ignore changes
 * @param {Event} event Browser event
 * @param {EventResizeData[]} resizeData Data of all events that are being resized
 */
/**
 * Fires on the owning Scheduler after the resizing gesture has finished.
 * @event eventResizeEnd
 * @on-owner
 * @param {Scheduler.view.Scheduler} source Scheduler instance
 * @param {Boolean} changed Shows if the record has been changed by the resize action
 * @param {Scheduler.model.EventModel} eventRecord Event record being resized
 * @param {EventResizeData[]} resizeData Resize data for selected events
 */
//endregion
//region Config
__publicField(EventResize, "$name", "EventResize");
EventResize._$name = "EventResize";
GridFeatureManager.registerFeature(EventResize, true, "Scheduler");
GridFeatureManager.registerFeature(EventResize, false, "ResourceHistogram");

// ../Scheduler/lib/Scheduler/feature/mixin/TaskEditStm.js
var TaskEditStm_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    getStmCapture() {
      var _a6, _b2;
      return Object.assign((_b2 = (_a6 = super.getStmCapture) == null ? void 0 : _a6.call(this)) != null ? _b2 : {}, {
        stmInitiallyAutoRecord: this.stmInitiallyAutoRecord,
        stmInitiallyDisabled: this.stmInitiallyDisabled,
        // this flag indicates whether the STM capture has been transferred to
        // another feature, which will be responsible for finalizing the STM transaction
        // (otherwise we'll do it ourselves)
        transferred: false
      });
    }
    applyStmCapture(stmCapture) {
      this.stmInitiallyAutoRecord = stmCapture.stmInitiallyAutoRecord;
      this.stmInitiallyDisabled = stmCapture.stmInitiallyDisabled;
      this.hasStmCapture = true;
      this.stmCapture = stmCapture;
      stmCapture.transferred = true;
    }
    captureStm(startTransaction = false) {
      const me = this, project = me.project, stm = project.getStm();
      if (me.hasStmCapture) {
        return;
      }
      me.hasStmCapture = true;
      me.stmInitiallyDisabled = stm.disabled;
      me.stmInitiallyAutoRecord = stm.autoRecord;
      if (me.stmInitiallyDisabled) {
        stm.enable();
        stm.autoRecord = false;
      } else {
        if (me.stmInitiallyAutoRecord) {
          stm.autoRecord = false;
        }
        if (stm.isRecording) {
          stm.stopTransaction();
        }
      }
      if (startTransaction) {
        this.startStmTransaction();
      }
    }
    startStmTransaction() {
      this.project.getStm().startTransaction();
    }
    commitStmTransaction() {
      const me = this, stm = me.project.getStm();
      if (!me.hasStmCapture) {
        throw new Error("Does not have STM capture, no transaction to commit");
      }
      if (stm.enabled) {
        stm.stopTransaction();
        if (me.stmInitiallyDisabled) {
          stm.resetQueue();
        }
      }
    }
    async rejectStmTransaction() {
      var _a6;
      const stm = this.project.getStm(), { client } = this;
      if (!this.hasStmCapture) {
        throw new Error("Does not have STM capture, no transaction to reject");
      }
      if (stm.enabled) {
        if ((_a6 = stm.transaction) == null ? void 0 : _a6.length) {
          client.suspendRefresh();
          stm.rejectTransaction();
          await client.resumeRefresh(true);
        } else if (stm.isRecording) {
          stm.stopTransaction();
        }
      }
    }
    enableStm() {
      this.project.getStm().enable();
    }
    disableStm() {
      this.project.getStm().disable();
    }
    async freeStm(commitOrReject = null) {
      const me = this, stm = me.project.getStm(), {
        stmInitiallyDisabled,
        stmInitiallyAutoRecord
      } = me;
      if (!me.hasStmCapture) {
        return;
      }
      let promise;
      me.rejectingStmTransaction = true;
      if (commitOrReject === true) {
        promise = me.commitStmTransaction();
      } else if (commitOrReject === false) {
        promise = me.rejectStmTransaction();
      }
      me.hasStmCapture = false;
      me.stmCapture = null;
      await promise;
      if (!stm.isDestroying) {
        stm.disabled = stmInitiallyDisabled;
        stm.autoRecord = stmInitiallyAutoRecord;
      }
      if (!me.isDestroying) {
        me.rejectingStmTransaction = false;
      }
    }
  }, __publicField(_a5, "$name", "TaskEditStm"), _a5;
};

// ../Scheduler/lib/Scheduler/feature/mixin/TaskEditTransactional.js
var TaskEditTransactional_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    captureStm(force) {
      if (this.client.transactionalFeaturesEnabled) {
        super.captureStm();
        return this.startStmTransaction(force);
      } else {
        super.captureStm(force);
      }
    }
    freeStm(commitOrReject) {
      if (this.hasStmCapture || !this.client.transactionalFeaturesEnabled) {
        return super.freeStm(commitOrReject);
      }
    }
    async startStmTransaction(startRecordingEarly) {
      if (this.client.transactionalFeaturesEnabled) {
        await this.startFeatureTransaction(startRecordingEarly);
      } else {
        super.startStmTransaction();
      }
    }
    commitStmTransaction() {
      if (this.client.transactionalFeaturesEnabled) {
        return this.finishFeatureTransaction();
      } else {
        super.commitStmTransaction();
      }
    }
    afterCancel() {
      if (this.client.transactionalFeaturesEnabled) {
        this.rejectFeatureTransaction();
      }
    }
  }, __publicField(_a5, "$name", "TaskEditTransactional"), _a5;
};

// ../Scheduler/lib/Scheduler/feature/base/DragCreateBase.js
var getDragCreateDragDistance = function(event) {
  var _a5, _b2;
  if ((_b2 = (_a5 = this.source) == null ? void 0 : _a5.client.features.taskEdit) == null ? void 0 : _b2._canceling) {
    return false;
  }
  return EventHelper.getDistanceBetween(this.startEvent, event);
};
var DragCreateBase = class extends EventResize.mixin(
  TaskEditStm_default,
  TaskEditTransactional_default
) {
  static get pluginConfig() {
    return {
      chain: ["render", "onEventDataGenerated"],
      before: ["onElementContextMenu"]
    };
  }
  construct(scheduler, config) {
    if ((config == null ? void 0 : config.showTooltip) === false) {
      config.tip = null;
    }
    super.construct(...arguments);
  }
  //endregion
  changeValidatorFn(validatorFn) {
    this.createValidatorFn = validatorFn;
  }
  render() {
    const me = this, { client } = me;
    me.dragRootElement = me.dropRootElement = client.timeAxisSubGridElement;
    me.dragLock = client.isVertical ? "y" : "x";
  }
  onDragEndSwitch(context) {
    const { client } = this, { enableEventAnimations } = client, { eventRecord } = context, { draggingEnd } = context.mainContext, horizontal = this.dragLock === "x", { initialDate } = this.dragging;
    client.enableEventAnimations = false;
    eventRecord.set({
      startDate: initialDate,
      endDate: initialDate
    });
    if (draggingEnd) {
      context.endDate = initialDate;
      Object.assign(context.mainContext, {
        toSet: "startDate",
        otherEnd: "endDate",
        setMethod: "setStartDate",
        setOtherMethod: "setEndDate",
        edge: horizontal ? "left" : "top"
      });
    } else {
      context.startDate = initialDate;
      Object.assign(context.mainContext, {
        toSet: "endDate",
        otherEnd: "startDate",
        setMethod: "setEndDate",
        setOtherMethod: "setStartDate",
        edge: horizontal ? "right" : "bottom"
      });
    }
    context.mainContext.draggingEnd = this.draggingEnd = !draggingEnd;
    client.enableEventAnimations = enableEventAnimations;
  }
  beforeDrag(drag) {
    const me = this, result = super.beforeDrag(drag), { pan, eventDragSelect } = me.client.features;
    if (result === false || // used by gantt to only allow one task per row
    (me.preventMultiple && !me.isRowEmpty(drag.rowRecord) || me.disabled || // If Pan is enabled, it has right of way
    pan && !pan.disabled || // If EventDragSelect is enabled, it has right of way
    eventDragSelect && !eventDragSelect.disabled)) {
      return false;
    }
    me.client.preventDragSelect = true;
    return result;
  }
  startDrag(drag) {
    const result = super.startDrag(drag);
    if (result !== false) {
      const [context] = drag.context.eventContexts;
      drag.initialDate = context.eventRecord.get(this.draggingEnd ? "startDate" : "endDate");
      this.client.trigger("dragCreateStart", {
        proxyElement: drag.element,
        eventElement: drag.element,
        eventRecord: context.eventRecord,
        resourceRecord: context.resourceRecord
      });
      drag.context.offset = 0;
      drag.context.oldValue = drag.mousedownDate;
    }
    return result;
  }
  // Used by our EventResize superclass to know whether the drag point is the end or the beginning.
  isOverEndHandle() {
    return this.draggingEnd;
  }
  setupDragContext(event) {
    const { client } = this;
    if (client.matchScheduleCell(event.target)) {
      const resourceRecordLink = client.resolveResourceRecord(event), resourceRecord = resourceRecordLink == null ? void 0 : resourceRecordLink.$original;
      if (resourceRecord && !resourceRecord.isSpecialRow) {
        const result = Draggable_default().prototype.setupDragContext.call(this, event), scrollables = [];
        if (client.isVertical) {
          scrollables.push({
            element: client.scrollable.element,
            direction: "vertical"
          });
        } else {
          scrollables.push({
            element: client.timeAxisSubGrid.scrollable.element,
            direction: "horizontal"
          });
        }
        result.scrollManager = client.scrollManager;
        result.monitoringConfig = { scrollables };
        result.resourceRecord = result.rowRecord = resourceRecord;
        if (resourceRecordLink !== resourceRecord) {
          result.resourceRecordLink = resourceRecordLink;
        }
        result.getDistance = getDragCreateDragDistance;
        return result;
      }
    }
  }
  async dragDrop({ context, event }) {
    var _a5;
    const { client } = this, { toSet } = context, [eventContext] = context.eventContexts;
    eventContext[toSet] = eventContext.snappedDate;
    const {
      startDate,
      endDate,
      eventRecord
    } = eventContext, { generation } = eventRecord;
    let modified;
    (_a5 = this.tip) == null ? void 0 : _a5.hide();
    await client.project.commitAsync();
    if (eventRecord.generation !== generation) {
      eventRecord[toSet] = eventContext.oldValue;
      eventRecord[toSet] = eventContext[toSet];
    }
    context.valid = startDate && endDate && endDate - startDate > 0 && // Input sanity check
    eventContext[toSet] - eventContext.oldValue && // Make sure dragged end changed
    context.valid !== false;
    if (context.valid) {
      context.startDate = eventContext.startDate;
      context.endDate = eventContext.endDate;
      client.trigger("beforeDragCreateFinalize", {
        context,
        event,
        proxyElement: eventContext.element,
        eventElement: eventContext.element,
        eventRecord: eventContext.eventRecord,
        resourceRecord: eventContext.resourceRecord
      });
      modified = true;
    }
    if (!context.async) {
      await context.finalize(modified);
    }
  }
  updateDragTolerance(dragTolerance) {
    this.dragThreshold = dragTolerance;
  }
  //region Tooltip
  changeTip(tip, oldTip) {
    return super.changeTip(!tip || tip.isTooltip ? tip : ObjectHelper.assign({
      id: `${this.client.id}-drag-create-tip`
    }, tip), oldTip);
  }
  //endregion
  //region Finalize (create EventModel)
  // this method is actually called on the `context` object,
  // so `this` object inside might not be what you think (see `me = this.owner` below)
  // not clear what was the motivation for such design
  async finalize(doCreate) {
    var _a5;
    if (this.finalized) {
      return;
    }
    this.finalized = true;
    const me = this.owner, context = this, [eventContext] = context.eventContexts, completeFinalization = () => {
      if (!me.isDestroyed) {
        me.client.trigger("afterDragCreate", {
          proxyElement: eventContext.element,
          eventElement: eventContext.element,
          eventRecord: eventContext.eventRecord,
          resourceRecord: eventContext.resourceRecord
        });
        me.cleanup(context);
      }
    };
    if (doCreate) {
      await me.finalizeDragCreate(context);
      completeFinalization();
    } else {
      await me.cancelDragCreate(context);
      (_a5 = me.onAborted) == null ? void 0 : _a5.call(me, context);
      completeFinalization();
    }
  }
  async cancelDragCreate(context) {
  }
  async finalizeDragCreate(context) {
    const me = this, { client } = me, [eventContext] = context.eventContexts, { eventRecord, resourceRecord } = eventContext;
    if (client.snapRelativeToEventStartDate) {
      eventContext[context.toSet] = eventContext.snappedDate = client.timeAxis.roundDate(eventContext.date, eventContext.oldValue);
    }
    me.adjustRecordBeforeUpdating(eventContext, eventRecord);
    client.endListeningForBatchedUpdates();
    await me.internalUpdateRecord(eventContext, eventRecord);
    const stmCapture = me.getStmCapture();
    !client.isDestroyed && client.trigger("dragCreateEnd", {
      eventRecord,
      resourceRecord,
      event: context.event,
      eventElement: eventContext.element,
      stmCapture
    });
    !client.isDestroyed && client.trigger("eventAutoCreated", {
      eventRecord,
      resourceRecord
    });
    return stmCapture.transferred;
  }
  cleanup(context) {
    var _a5;
    const { client } = this, [eventContext] = context.eventContexts;
    eventContext.eventRecord.meta.isResizing = false;
    client.endListeningForBatchedUpdates();
    (_a5 = this.tip) == null ? void 0 : _a5.hide();
    client.element.classList.remove(...this.dragActiveCls.split(" "));
    eventContext.element.parentElement.classList.remove("b-sch-dragcreating");
  }
  //endregion
  //region Events
  /**
   * Prevent right click when drag creating
   * @returns {Boolean}
   * @private
   */
  onElementContextMenu() {
    if (this.proxy) {
      return false;
    }
  }
  prepareCreateContextForFinalization(createContext, event, finalize, async = false) {
    return {
      ...createContext,
      async,
      event,
      finalize
    };
  }
  // Apply drag create "proxy" styling
  onEventDataGenerated(renderData) {
    var _a5, _b2;
    if (((_b2 = (_a5 = this.dragging) == null ? void 0 : _a5.context) == null ? void 0 : _b2.eventRecord) === renderData.eventRecord) {
      renderData.wrapperCls["b-sch-dragcreating"] = true;
      renderData.wrapperCls["b-too-narrow"] = this.dragging.context.tooNarrow;
    }
  }
  //endregion
  get isDragCreating() {
    const { dragging } = this;
    return dragging && dragging.state === dragging.constructor.STATE.DRAGGING;
  }
  //region Product specific, implemented in subclasses
  // Empty implementation here. Only base EventResize class triggers this
  triggerBeforeResize() {
  }
  // Empty implementation here. Only base EventResize class triggers this
  triggerEventResizeStart() {
  }
  checkValidity(context, event) {
    throw new Error("Implement in subclass");
  }
  handleBeforeDragCreate(dateTime, event) {
    throw new Error("Implement in subclass");
  }
  isRowEmpty(rowRecord) {
    throw new Error("Implement in subclass");
  }
  //endregion
};
//region Config
__publicField(DragCreateBase, "configurable", {
  /**
   * true to show a time tooltip when dragging to create a new event
   * @config {Boolean}
   * @default
   */
  showTooltip: true,
  /**
   * Number of pixels the drag target must be moved before dragging is considered to have started. Defaults to 2.
   * @config {Number}
   * @default
   */
  dragTolerance: 2,
  // used by gantt to only allow one task per row
  preventMultiple: false,
  dragTouchStartDelay: 300,
  /**
   * `this` reference for the validatorFn
   * @config {Object}
   */
  validatorFnThisObj: null,
  tipTemplate: (data) => `
            <div class="b-sch-tip-${data.valid ? "valid" : "invalid"}">
                ${data.startClockHtml}
                ${data.endClockHtml}
                <div class="b-sch-tip-message">${data.message}</div>
            </div>
        `,
  dragActiveCls: "b-dragcreating"
});
DragCreateBase._$name = "DragCreateBase";

// ../Scheduler/lib/Scheduler/feature/base/EditBase.js
var DH3 = DateHelper;
var scheduleFields = ["startDate", "endDate", "resource", "recurrenceRule"];
var makeDate = (fields) => {
  if (fields.length === 1)
    return fields[0].value;
  else if (fields.length === 2) {
    const [date2, time] = fields[0] instanceof DateField ? fields : fields.reverse(), dateValue = DH3.parse(date2.value);
    if (dateValue && time.value) {
      dateValue.setHours(
        time.value.getHours(),
        time.value.getMinutes(),
        time.value.getSeconds(),
        time.value.getMilliseconds()
      );
    }
    return dateValue ? DateHelper.clone(dateValue) : null;
  }
  return null;
};
var copyTime = (dateTo, dateFrom) => {
  const d = new Date(dateTo.getTime());
  d.setHours(dateFrom.getHours(), dateFrom.getMinutes());
  return d;
};
var adjustEndDate = (startDate, startTime, me) => {
  if (!me.editor.assigningValues && startDate && startTime && me.endDateField && me.endTimeField) {
    const newEndDate = DH3.add(copyTime(me.startDateField.value, me.startTimeField.value), me._durationMS, "milliseconds");
    me.endDateField.value = newEndDate;
    me.endTimeField.value = DH3.clone(newEndDate);
  }
};
var EditBase = class extends InstancePlugin {
  //region Config
  static get configurable() {
    return {
      /**
       * True to save and close this panel if ENTER is pressed in one of the input fields inside the panel.
       * @config {Boolean}
       * @default
       * @category Editor
       */
      saveAndCloseOnEnter: true,
      triggerEvent: null,
      /**
       * This config parameter is passed to the `startDateField` and `endDateField` constructor.
       * @config {String}
       * @default
       * @category Editor widgets
       */
      dateFormat: "L",
      // date format that uses browser locale
      /**
       * This config parameter is passed to the `startTimeField` and `endTimeField` constructor.
       * @config {String}
       * @default
       * @category Editor widgets
       */
      timeFormat: "LT",
      // date format that uses browser locale
      /**
       * Default editor configuration, which widgets it shows etc.
       *
       * This is the entry point into configuring any aspect of the editor.
       *
       * The {@link Core.widget.Container#config-items} configuration of a Container
       * is *deeply merged* with its default `items` value. This means that you can specify
       * an `editorConfig` object which configures the editor, or widgets inside the editor:
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         eventEdit  : {
       *             editorConfig : {
       *                 autoClose : false,
       *                 modal     : true,
       *                 cls       : 'editor-widget-cls',
       *                 items : {
       *                     resourceField : {
       *                         hidden : true
       *                     },
       *                     // Add our own event owner field at the top of the form.
       *                     // Weight -100 will make it sort top the top.
       *                     ownerField : {
       *                         weight : -100,
       *                         type   : 'usercombo',
       *                         name   : 'owner',
       *                         label  : 'Owner'
       *                     }
       *                 },
       *                 bbar : {
       *                     items : {
       *                         deleteButton : false
       *                     }
       *                 }
       *             }
       *         }
       *     }
       * });
       * ```
       * @config {PopupConfig}
       * @category Editor
       */
      editorConfig: null,
      /**
       * An object to merge with the provided items config of the editor to override the
       * configuration of provided fields, or add new fields.
       *
       * To remove existing items, set corresponding keys to `null`:
       *
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         eventEdit  : {
       *             items : {
       *                 // Merged with provided config of the resource field
       *                 resourceField : {
       *                     label : 'Calendar'
       *                 },
       *                 recurrenceCombo : null,
       *                 owner : {
       *                     weight : -100, // Will sort above system-supplied fields which are weight 0
       *                     type   : 'usercombo',
       *                     name   : 'owner',
       *                     label  : 'Owner'
       *                 }
       *             }
       *         }
       *     }
       * });
       * ```
       *
       * The provided fields are called
       *  - `nameField`
       *  - `resourceField`
       *  - `startDateField`
       *  - `startTimeField`
       *  - `endDateField`
       *  - `endTimeField`
       *  - `recurrenceCombo`
       *  - `editRecurrenceButton`
       * @config {Object<String,ContainerItemConfig|Boolean|null>}
       * @category Editor widgets
       */
      items: null,
      /**
       * The week start day used in all date fields of the feature editor form by default.
       * 0 means Sunday, 6 means Saturday.
       * Defaults to the locale's week start day.
       * @config {Number}
       */
      weekStartDay: null
    };
  }
  //endregion
  //region Init & destroy
  construct(client, config) {
    const me = this;
    client.eventEdit = me;
    super.construct(client, ObjectHelper.assign({
      weekStartDay: client.weekStartDay
    }, config));
    me.clientListenersDetacher = client.ion({
      [me.triggerEvent]: "onActivateEditor",
      dragCreateEnd: "onDragCreateEnd",
      // Not fired at the Scheduler level.
      // Calendar, which inherits this, implements this event.
      eventAutoCreated: "onEventAutoCreated",
      thisObj: me
    });
  }
  doDestroy() {
    var _a5;
    this.clientListenersDetacher();
    (_a5 = this._editor) == null ? void 0 : _a5.destroy();
    super.doDestroy();
  }
  //endregion
  //region Editing
  // Not implemented at this level.
  // Scheduler Editing relies on being called at point of event creation.
  onEventAutoCreated() {
  }
  changeEditorConfig(editorConfig) {
    const { items: items2 } = this;
    if (items2) {
      editorConfig = Objects.clone(editorConfig);
      editorConfig.items = Config.merge(items2, editorConfig.items);
    }
    return editorConfig;
  }
  changeItems(items2) {
    this.cleanItemsConfig(items2);
    return items2;
  }
  // Remove any items configured as === true which just means default config options
  cleanItemsConfig(items2) {
    for (const ref in items2) {
      const itemCfg = items2[ref];
      if (itemCfg === true) {
        delete items2[ref];
      } else if (itemCfg == null ? void 0 : itemCfg.items) {
        this.cleanItemsConfig(itemCfg.items);
      }
    }
  }
  onDatesChange({ value: value2, source }) {
    var _a5, _b2, _c2, _d, _e, _f, _g, _h, _i, _j, _k;
    const me = this;
    if (value2 && source === me.startDateField && ((_a5 = me.startTimeField) == null ? void 0 : _a5.keepDate) && me.startTimeField.value) {
      me.startTimeField.value.setFullYear(value2.getFullYear());
      me.startTimeField.value.setMonth(value2.getMonth());
      me.startTimeField.value.setDate(value2.getDate());
    }
    if ((source === me.endDateField || source === me.endTimeField) && me.startDateField) {
      const newEndDate = ((_b2 = me.endTimeField) == null ? void 0 : _b2.value) && ((_c2 = me.endDateField) == null ? void 0 : _c2.value) ? copyTime(me.endDateField.value, me.endTimeField.value) : (_d = me.endDateField) == null ? void 0 : _d.value, newStartDate = ((_e = me.startTimeField) == null ? void 0 : _e.value) && ((_f = me.startDateField) == null ? void 0 : _f.value) ? copyTime(me.startDateField.value, me.startTimeField.value) : (_g = me.startDateField) == null ? void 0 : _g.value;
      if (newEndDate && newStartDate) {
        me._durationMS = newEndDate - newStartDate;
      }
    }
    if (me.startDateField && me.endDateField) {
      me.endDateField.min = me.startDateField.value;
    }
    if (me.endTimeField) {
      if (DH3.isEqual(DH3.clearTime((_h = me.startDateField) == null ? void 0 : _h.value), DH3.clearTime((_i = me.endDateField) == null ? void 0 : _i.value))) {
        me.endTimeField.min = me.startTimeField.value;
      } else {
        me.endTimeField.min = null;
      }
    }
    switch (source.ref) {
      case "startDateField":
        ((_j = me.startTimeField) == null ? void 0 : _j.value) && adjustEndDate(value2, me.startTimeField.value, me);
        break;
      case "startTimeField":
        ((_k = me.startDateField) == null ? void 0 : _k.value) && adjustEndDate(me.startDateField.value, value2, me);
        break;
    }
  }
  //endregion
  //region Save
  async save() {
    throw new Error("Implement in subclass");
  }
  get values() {
    const me = this, { editor } = me, startFields = [], endFields = [], { values } = editor;
    scheduleFields.forEach((f) => delete values[f]);
    editor.eachWidget((widget) => {
      var _a5;
      const { name } = widget;
      if (!name || widget.hidden || widget.up((w) => w === me.recurrenceEditor)) {
        delete values[name];
        return;
      }
      switch (name) {
        case "startDate":
          startFields.push(widget);
          break;
        case "endDate":
          endFields.push(widget);
          break;
        case "resource":
          values[name] = widget.record;
          break;
        case "recurrenceRule":
          values[name] = ((_a5 = editor.widgetMap.recurrenceCombo) == null ? void 0 : _a5.value) === "none" ? "" : widget.value;
          break;
      }
    }, true);
    if (values.allDay && !me.eventRecord.allDay) {
      startFields.push(me.startTimeField);
      endFields.push(me.endTimeField);
    }
    if (startFields.length) {
      values.startDate = makeDate(startFields);
    }
    if (endFields.length) {
      values.endDate = makeDate(endFields);
    }
    if ("startDate" in values && "endDate" in values) {
      values.duration = DH3.diff(values.startDate, values.endDate, me.editor.record.durationUnit, true);
    }
    return values;
  }
  /**
   * Template method, intended to be overridden. Called before the event record has been updated.
   * @param {Scheduler.model.EventModel} eventRecord The event record
   *
   **/
  onBeforeSave(eventRecord) {
  }
  /**
   * Template method, intended to be overridden. Called after the event record has been updated.
   * @param {Scheduler.model.EventModel} eventRecord The event record
   *
   **/
  onAfterSave(eventRecord) {
  }
  /**
   * Updates record being edited with values from the editor
   * @private
   */
  updateRecord(record) {
    var _a5;
    const { values } = this;
    if (this.assignmentStore) {
      delete values.resource;
    }
    this._durationMS = DateHelper.asMilliseconds((_a5 = values.duration) != null ? _a5 : record.duration, record.durationUnit);
    return record.set(values);
  }
  //endregion
  //region Events
  onBeforeEditorShow() {
    const { eventRecord, editor } = this.editingContext, { nameField } = editor.widgetMap;
    if (nameField && eventRecord.isCreating) {
      editor.assigningValues = true;
      nameField.value = "";
      editor.assigningValues = false;
      nameField._configuredPlaceholder = nameField.placeholder;
      nameField.placeholder = eventRecord.name;
    }
  }
  resetEditingContext() {
    var _a5;
    const me = this;
    if (!me.editingContext) {
      return;
    }
    const { client } = me, { editor, eventRecord } = me.editingContext, { eventStore } = client, { nameField } = editor.widgetMap;
    if (eventRecord.isCreating) {
      if (client.isTimelineBase) {
        (_a5 = me.editingContext.eventElement) == null ? void 0 : _a5.closest("[data-event-id]").classList.add("b-released");
      }
      eventStore.remove(eventRecord);
      eventRecord.isCreating = false;
    }
    if (nameField) {
      nameField.placeholder = nameField._configuredPlaceholder;
    }
    client.element.classList.remove("b-eventeditor-editing");
    me.targetEventElement = me.editingContext = editor._record = null;
  }
  onPopupKeyDown({ event }) {
    const me = this;
    if (!me.readOnly && event.key === "Enter" && me.saveAndCloseOnEnter && event.target.tagName.toLowerCase() === "input") {
      event.preventDefault();
      if (event.target.name === "startDate") {
        me.startTimeField && adjustEndDate(me.startDateField.value, me.startTimeField.value, me);
      }
      me.onSaveClick();
    }
  }
  async finalizeStmCapture(saved) {
  }
  async onSaveClick() {
    const me = this;
    me.editor.focus();
    me.isFinalizingEventSave = true;
    const saved = await me.save();
    me.isFinalizingEventSave = false;
    if (saved) {
      await me.finalizeStmCapture(false);
      if (!me.isDestroyed) {
        me.editor.close(me);
        me.client.trigger("afterEventEdit", { ...me.editingContext, action: "save" });
      }
    }
    return saved;
  }
  async onDeleteClick() {
    const me = this;
    me.isDeletingEvent = true;
    const removed = await me.deleteEvent();
    me.isDeletingEvent = false;
    if (removed) {
      await me.finalizeStmCapture(false);
      if (!me.isDestroyed) {
        me.editor.close(me);
        me.client.trigger("afterEventEdit", { ...me.editingContext, action: "delete" });
      }
    }
  }
  async onCancelClick() {
    const me = this;
    me.isCancelingEdit = true;
    me.editor.close(me);
    me.isCancelingEdit = false;
    if (me.hasStmCapture) {
      await me.finalizeStmCapture(true);
    }
    if (!me.isDestroyed) {
      me.client.trigger("afterEventEdit", { ...me.editingContext, action: "cancel" });
    }
  }
  //endregion
};
EditBase._$name = "EditBase";

// ../Scheduler/lib/Scheduler/feature/base/ResourceTimeRangesBase.js
var ResourceTimeRangesBase = class extends InstancePlugin.mixin(AttachToProjectMixin_default) {
  static get pluginConfig() {
    return {
      before: ["onInternalPaint"],
      chain: ["getEventsToRender", "onEventDataGenerated", "noFeatureElementsInAxis"],
      override: ["matchScheduleCell", "resolveResourceRecord"]
    };
  }
  // Let Scheduler know if we have ResourceTimeRanges in view or not
  noFeatureElementsInAxis() {
    const { timeAxis } = this.client;
    return !this.needsRefresh && this.store && !this.store.storage.values.some((t) => timeAxis.isTimeSpanInAxis(t));
  }
  //endregion
  //region Init
  doDisable(disable) {
    if (this.client.isPainted) {
      this.client.refresh();
    }
    super.doDisable(disable);
  }
  updateTabIndex() {
    if (!this.isConfiguring) {
      this.client.refresh();
    }
  }
  onInternalPaint({ firstPaint }) {
    firstPaint && this.setupCanvas();
  }
  setupCanvas() {
    const { entityName, client } = this;
    if (!this.canvas) {
      this.canvas = client.canvases[entityName] = DomHelper.createElement({
        className: `b-${StringHelper.hyphenate(entityName)}-canvas b-sch-canvas`,
        parent: client.timeAxisSubGridElement,
        retainElement: true
      });
    }
  }
  //endregion
  getEventsToRender(resource, events) {
    throw new Error("Implement in subclass");
  }
  // Called for each event during render, allows manipulation of render data. Adjust any resource time ranges
  // (chained function from Scheduler)
  onEventDataGenerated(renderData) {
    const me = this, { client } = me, { eventRecord, iconCls } = renderData;
    if (me.shouldInclude(eventRecord)) {
      if (client.isVertical) {
        renderData.width = client.getResourceWidth(renderData.resourceRecord);
      } else {
        renderData.top = 0;
      }
      renderData.entityName = me.entityName;
      renderData.fillSize = true;
      renderData.wrapperCls["b-sch-resourcetimerange"] = 1;
      if (me.rangeCls) {
        renderData.wrapperCls[me.rangeCls] = 1;
      }
      renderData.wrapperCls[`b-sch-color-${eventRecord.timeRangeColor}`] = eventRecord.timeRangeColor;
      me.renderContent(eventRecord, renderData);
      renderData.children.push(renderData.eventContent);
      renderData.tabIndex = me.tabIndex != null ? String(me.tabIndex) : null;
      if ((iconCls == null ? void 0 : iconCls.length) > 0) {
        renderData.children.unshift({
          tag: "i",
          className: iconCls.toString()
        });
      }
      renderData.eventId = me.generateElementId(eventRecord);
    }
  }
  renderContent(eventRecord, renderData) {
    renderData.eventContent.text = eventRecord.name;
  }
  /**
   * Generates ID from the passed time range record
   * @param {Scheduler.model.TimeSpan} record
   * @returns {String} Generated ID for the DOM element
   * @internal
   */
  generateElementId(record) {
    return record.domId;
  }
  resolveResourceTimeRangeRecord(rangeElement) {
    var _a5;
    return (_a5 = rangeElement == null ? void 0 : rangeElement.closest(`.${this.rangeCls}`)) == null ? void 0 : _a5.elementData.eventRecord;
  }
  getElementFromResourceTimeRangeRecord(record) {
    return this.canvas.syncIdMap[record.domId];
  }
  resolveResourceRecord(event) {
    var _a5;
    const record = this.overridden.resolveResourceRecord(...arguments);
    return record || ((_a5 = this.resolveResourceTimeRangeRecord(event.target || event)) == null ? void 0 : _a5.resource);
  }
  shouldInclude(eventRecord) {
    throw new Error("Implement in subclass");
  }
  // Called when a ResourceTimeRangeModel is manipulated, relays to Scheduler#onInternalEventStoreChange which updates to UI
  onStoreChange(event) {
    const { action } = event;
    if (action === "removeall" || action === "dataset" || action === "lazyload") {
      this.needsRefresh = true;
    }
    this.client.onInternalEventStoreChange(event);
    this.needsRefresh = false;
  }
  // Override to let scheduler find the time cell from a resource time range element
  matchScheduleCell(target) {
    let cell = this.overridden.matchScheduleCell(target);
    if (!cell && this.enableMouseEvents) {
      const { client } = this, rangeElement = target.closest(`.${this.rangeCls}`);
      cell = rangeElement && client.getCell({
        record: client.isHorizontal ? rangeElement.elementData.resource : client.store.first,
        column: client.timeAxisColumn
      });
    }
    return cell;
  }
  handleRangeMouseEvent(domEvent) {
    var _a5;
    const me = this, rangeElement = domEvent.target.closest(`.${me.rangeCls}`);
    if (rangeElement) {
      const eventName = (_a5 = EventHelper.eventNameMap[domEvent.type]) != null ? _a5 : StringHelper.capitalize(domEvent.type), resourceTimeRangeRecord = me.resolveResourceTimeRangeRecord(rangeElement);
      me.client.trigger(me.entityName + eventName, {
        feature: me,
        [`${me.entityName}Record`]: resourceTimeRangeRecord,
        resourceRecord: me.client.resourceStore.getById(resourceTimeRangeRecord.resourceId),
        startDate: resourceTimeRangeRecord.startDate,
        endDate: resourceTimeRangeRecord.endDate,
        domEvent
      });
    }
  }
  updateEnableMouseEvents(enable) {
    var _a5;
    const me = this, { client } = me;
    (_a5 = me.mouseEventsDetacher) == null ? void 0 : _a5.call(me);
    me.mouseEventsDetacher = null;
    client.whenVisible(() => {
      if (enable) {
        me.mouseEventsDetacher = EventHelper.on({
          element: client.canvases[me.entityName],
          delegate: `.${me.rangeCls}`,
          mousedown: "handleRangeMouseEvent",
          mouseup: "handleRangeMouseEvent",
          click: "handleRangeMouseEvent",
          dblclick: "handleRangeMouseEvent",
          contextmenu: "handleRangeMouseEvent",
          mouseover: "handleRangeMouseEvent",
          mouseout: "handleRangeMouseEvent",
          thisObj: me
        });
      }
      me.canvas.classList.toggle("b-interactive", Boolean(enable));
    });
  }
};
//region Config
__publicField(ResourceTimeRangesBase, "configurable", {
  /**
   * Specify value to use for the tabIndex attribute of range elements
   * @config {Number}
   * @category Misc
   */
  tabIndex: null,
  entityName: "resourceTimeRange"
});
ResourceTimeRangesBase.featureClass = "";
ResourceTimeRangesBase._$name = "ResourceTimeRangesBase";

// ../Scheduler/lib/Scheduler/feature/base/TimeSpanMenuBase.js
var TimeSpanMenuBase = class extends ContextMenuBase {
};
TimeSpanMenuBase._$name = "TimeSpanMenuBase";

// ../Scheduler/lib/Scheduler/feature/base/TooltipBase.js
var id = 0;
var TooltipBase = class extends InstancePlugin {
  //region Config
  static get defaultConfig() {
    return {
      /**
       * Specify true to have tooltip updated when mouse moves, if you for example want to display date at mouse
       * position.
       * @config {Boolean}
       * @default
       * @category Misc
       */
      autoUpdate: false,
      /**
       * The amount of time to hover before showing
       * @config {Number}
       * @default
       */
      hoverDelay: 250,
      /**
       * The time (in milliseconds) for which the Tooltip remains visible when the mouse leaves the target.
       *
       * May be configured as `false` to persist visible after the mouse exits the target element. Configure it
       * as 0 to always retrigger `hoverDelay` even when moving mouse inside `fromElement`
       * @config {Number}
       * @default
       */
      hideDelay: 100,
      template: null,
      cls: null,
      align: {
        align: "b-t"
      },
      clockTemplate: null,
      // Set to `true` to update tooltip contents if record changes while tip is open
      monitorRecordUpdate: null,
      testConfig: {
        hoverDelay: 0
      }
    };
  }
  // Plugin configuration. This plugin chains some of the functions in Grid.
  static get pluginConfig() {
    return {
      chain: ["onInternalPaint"]
    };
  }
  //endregion
  //region Events
  /**
   * Triggered before a tooltip is shown. Return `false` to prevent the action.
   * @preventable
   * @event beforeShow
   * @param {Core.widget.Tooltip|Object} source The tooltip being shown.
   * @param {Scheduler.model.EventModel} source.eventRecord The event record.
   */
  /**
   * Triggered after a tooltip is shown.
   * @event show
   * @param {Core.widget.Tooltip|Object} source The tooltip.
   * @param {Scheduler.model.EventModel} source.eventRecord The event record.
   */
  //endregion
  //region Init
  construct(client, config) {
    const me = this;
    config = me.processConfig(config);
    super.construct(client, config);
    if (!me.forSelector) {
      me.forSelector = `${client.eventInnerSelector}:not(.b-dragproxy,.b-iscreating)`;
    }
    me.clockTemplate = new ClockTemplate({
      scheduler: client
    });
    client.ion({
      [`before${client.scheduledEventName}drag`]: () => {
        var _a5;
        (_a5 = me.tooltip) == null ? void 0 : _a5.hide();
      }
    });
  }
  // TooltipBase feature handles special config cases, where user can supply a function to use as template
  // instead of a normal config object
  processConfig(config) {
    if (typeof config === "function") {
      return {
        template: config
      };
    }
    return config;
  }
  // override setConfig to process config before applying it (used mainly from ReactScheduler)
  setConfig(config) {
    super.setConfig(this.processConfig(config));
  }
  doDestroy() {
    this.destroyProperties("clockTemplate", "tooltip");
    super.doDestroy();
  }
  doDisable(disable) {
    if (this.tooltip) {
      this.tooltip.disabled = disable;
    }
    super.doDisable(disable);
  }
  //endregion
  onInternalPaint({ firstPaint }) {
    var _a5;
    if (firstPaint) {
      const me = this, { client } = me, ignoreSelector = `:not(${[
        ".b-dragselecting",
        ".b-eventeditor-editing",
        ".b-taskeditor-editing",
        ".b-resizing-event",
        ".b-task-percent-bar-resizing-task",
        ".b-dragcreating",
        `.b-dragging-${client.scheduledEventName}`,
        ".b-creating-dependency",
        ".b-dragproxy"
      ].join()})`;
      (_a5 = me.tooltip) == null ? void 0 : _a5.destroy();
      const tip = me.tooltip = new Tooltip({
        axisLock: "flexible",
        id: me.tipId || `${me.client.id}-event-tip-${id++}`,
        cls: me.tipCls,
        forSelector: `.b-timelinebase${ignoreSelector} .b-grid-body-container:not(.b-scrolling) ${me.forSelector}`,
        scrollAction: "realign",
        forElement: client.timeAxisSubGridElement,
        showOnHover: true,
        anchorToTarget: true,
        getHtml: me.getTipHtml.bind(me),
        disabled: me.disabled,
        // on Core/mixin/Events constructor, me.config.listeners is deleted and attributed its value to me.configuredListeners
        // to then on processConfiguredListeners it set me.listeners to our TooltipBase
        // but since we need our initial config.listeners to set to our internal tooltip, we leave processConfiguredListeners empty
        // to avoid lost our listeners to apply for our internal tooltip here and force our feature has all Tooltip events firing
        ...me.config,
        internalListeners: me.configuredListeners
      });
      tip.ion({
        innerhtmlupdate: "updateDateIndicator",
        overtarget: "onOverNewTarget",
        show: "onTipShow",
        hide: "onTipHide",
        thisObj: me
      });
      Object.keys(tip.$meta.configs).forEach((name) => {
        Object.defineProperty(this, name, {
          set: (v) => tip[name] = v,
          get: () => tip[name]
        });
      });
    }
  }
  //region Listeners
  // leave configuredListeners alone until render time at which they are used on the tooltip
  processConfiguredListeners() {
  }
  addListener(...args) {
    var _a5;
    const defaultDetacher = super.addListener(...args), tooltipDetacher = (_a5 = this.tooltip) == null ? void 0 : _a5.addListener(...args);
    if (defaultDetacher || tooltipDetacher) {
      return () => {
        defaultDetacher == null ? void 0 : defaultDetacher();
        tooltipDetacher == null ? void 0 : tooltipDetacher();
      };
    }
  }
  removeListener(...args) {
    var _a5;
    super.removeListener(...args);
    (_a5 = this.tooltip) == null ? void 0 : _a5.removeListener(...args);
  }
  //endregion
  updateDateIndicator() {
    const me = this, tip = me.tooltip, endDateElement = tip.element.querySelector(".b-sch-tooltip-enddate");
    if (!me.record) {
      return;
    }
    me.clockTemplate.updateDateIndicator(tip.element, me.record.startDate);
    endDateElement && me.clockTemplate.updateDateIndicator(endDateElement, me.record.endDate);
  }
  resolveTimeSpanRecord(forElement) {
    return this.client.resolveTimeSpanRecord(forElement);
  }
  resolveData(tip, activeTarget) {
    const me = this, { client } = me, recordProp = me.recordType || `${client.scheduledEventName}Record`, timeSpanRecord = me.resolveTimeSpanRecord(activeTarget);
    if (timeSpanRecord && !timeSpanRecord.isCommitting) {
      tip.eventRecord = tip.record = timeSpanRecord;
      const { startDate, endDate } = timeSpanRecord, startText = client.getFormattedDate(startDate), endDateValue = client.getDisplayEndDate(endDate, startDate), endText = client.getFormattedDate(endDateValue);
      return {
        tip,
        // eventRecord for Scheduler, taskRecord for Gantt
        [`${recordProp}`]: timeSpanRecord,
        startDate,
        endDate,
        startText,
        endText,
        startClockHtml: me.clockTemplate.template({
          date: startDate,
          text: startText,
          cls: "b-sch-tooltip-startdate"
        }),
        endClockHtml: timeSpanRecord.isMilestone ? "" : me.clockTemplate.template({
          date: endDateValue || "",
          text: endText || "",
          cls: "b-sch-tooltip-enddate"
        })
      };
    }
    return null;
  }
  getTipHtml({ tip, activeTarget }) {
    const templateData = this.resolveData(tip, activeTarget);
    if (!templateData || !this.template) {
      return;
    }
    if (!(templateData.startDate instanceof Date)) {
      tip.hide();
      return "";
    }
    return this.template(templateData);
  }
  get record() {
    return this.tooltip.eventRecord;
  }
  onTipShow() {
    const me = this;
    if (me.monitorRecordUpdate && !me.updateListener) {
      me.updateListener = me.client.eventStore.ion({
        change: me.onRecordUpdate,
        buffer: 300,
        thisObj: me
      });
    }
  }
  onTipHide() {
    var _a5;
    this.tooltip.eventRecord = this.tooltip.record = null;
    (_a5 = this.updateListener) == null ? void 0 : _a5.call(this);
    this.updateListener = null;
  }
  onOverNewTarget({ newTarget }) {
    const { tooltip } = this;
    if (tooltip.isVisible) {
      if (this.client.timeAxisSubGrid.scrolling || this.client.scrolling) {
        tooltip.hide(false);
      } else {
        tooltip.eventRecord = this.resolveTimeSpanRecord(newTarget);
      }
    }
  }
  onRecordUpdate({ record }) {
    const { tooltip } = this;
    if ((tooltip == null ? void 0 : tooltip.isVisible) && record === this.record) {
      tooltip.updateContent();
      if (tooltip.lastAlignSpec.aligningToElement) {
        tooltip.realign();
      } else {
        tooltip.internalOnPointerOver(this.client.lastPointerEvent);
      }
    }
  }
};
TooltipBase._$name = "TooltipBase";

// ../Scheduler/lib/Scheduler/feature/AbstractTimeRanges.js
var AbstractTimeRanges = class extends InstancePlugin.mixin(Delayable_default) {
  //region Config
  /**
   * Fired on the owning Scheduler or Gantt widget when a click happens on a time range header element
   * @event timeRangeHeaderClick
   * @on-owner
   * @param {Scheduler.view.Scheduler} source Scheduler instance
   * @param {Scheduler.model.TimeSpan} timeRangeRecord The record
   * @param {MouseEvent} domEvent Browser event
   */
  /**
   * Fired on the owning Scheduler or Gantt widget when a double click happens on a time range header element
   * @event timeRangeHeaderDblClick
   * @on-owner
   * @param {Scheduler.view.Scheduler} source Scheduler instance
   * @param {Scheduler.model.TimeSpan} timeRangeRecord The record
   * @param {MouseEvent} domEvent Browser event
   */
  /**
   * Fired on the owning Scheduler or Gantt widget when a right click happens on a time range header element
   * @event timeRangeHeaderContextMenu
   * @on-owner
   * @param {Scheduler.view.Scheduler} source Scheduler instance
   * @param {Scheduler.model.TimeSpan} timeRangeRecord The record
   * @param {MouseEvent} domEvent Browser event
   */
  static get defaultConfig() {
    return {
      // CSS class to apply to range elements
      rangeCls: "b-sch-range",
      // CSS class to apply to line elements (0-duration time range)
      lineCls: "b-sch-line",
      /**
       * Set to `true` to enable dragging and resizing of range elements in the header. Only relevant when
       * {@link #config-showHeaderElements} is `true`.
       * @config {Boolean}
       * @default
       * @category Common
       */
      enableResizing: false,
      /**
       * A Boolean specifying whether to show tooltip while resizing range elements, or a
       * {@link Core.widget.Tooltip} config object which is applied to the tooltip
       * @config {Boolean|TooltipConfig}
       * @default
       * @category Common
       */
      showTooltip: true,
      /**
       * The Tooltip instance shown when hovering a TimeRange header element
       * @member {Core.widget.Tooltip} hoverTooltip
       * @readonly
       */
      /**
       * A {@link Core.widget.Tooltip} config object which is applied to the tooltip shown when hovering a
       * TimeRange header element
       * @config {TooltipConfig}
       * @category Common
       */
      hoverTooltip: null,
      /**
       * Template used to generate the tooltip contents when hovering a time range header element.
       *
       * ```javascript
       * const scheduler = new Scheduler({
       *   features : {
       *     timeRanges : {
       *       tooltipTemplate({ timeRange }) {
       *         return `${timeRange.name}`
       *       }
       *     }
       *   }
       * });
       * ```
       *
       * @config {Function} tooltipTemplate
       * @param {Object} data Tooltip data
       * @param {Scheduler.model.TimeSpan} data.timeRange
       * @param {String} data.startClockHtml Predefined HTML to show the start time
       * @param {String} data.endClockHtml Predefined HTML to show the end time
       * @returns {String} String representing the HTML markup
       * @category Common
       */
      tooltipTemplate: null,
      dragTipTemplate: (data) => `
                <div class="b-sch-tip-${data.valid ? "valid" : "invalid"}">
                    <div class="b-sch-tip-name">${StringHelper.encodeHtml(data.name) || ""}</div>
                    ${data.startClockHtml}
                    ${data.endClockHtml || ""}
                </div>
            `,
      baseCls: "b-sch-timerange",
      /**
       * Function used to generate the HTML content for a time range header element.
       *
       * ```javascript
       * const scheduler = new Scheduler({
       *   features : {
       *     timeRanges : {
       *       headerRenderer({ timeRange }) {
       *         return `${timeRange.name}`
       *       }
       *     }
       *   }
       * });
       * ```
       *
       * @config {Function} headerRenderer
       * @param {Object} data Render data
       * @param {Scheduler.model.TimeSpan} data.timeRange
       * @returns {String} String representing the HTML markup
       *
       * @category Common
       */
      headerRenderer: null,
      /**
       * Function used to generate the HTML content for a time range body element.
       *
       * ```javascript
       * const scheduler = new Scheduler({
       *   features : {
       *     timeRanges : {
       *       bodyRenderer({ timeRange }) {
       *         return `${timeRange.name}`
       *       }
       *     }
       *   }
       * });
       * ```
       *
       * @config {Function} bodyRenderer
       * @param {Object} data Render data
       * @param {Scheduler.model.TimeSpan} data.timeRange
       * @returns {String} String representing the HTML markup
       *
       * @category Common
       */
      bodyRenderer: null,
      // a unique cls used by subclasses to get custom styling of the elements rendered
      cls: null,
      narrowThreshold: 80
    };
  }
  // Plugin configuration. This plugin chains some functions in Grid.
  static get pluginConfig() {
    return {
      chain: [
        "updateFillTicks",
        "onInternalPaint",
        "populateTimeAxisHeaderMenu",
        "onSchedulerHorizontalScroll",
        "afterScroll",
        "onInternalResize"
      ]
    };
  }
  //endregion
  //region Init & destroy
  construct(client, config) {
    const me = this;
    super.construct(client, config);
    if (client.isVertical) {
      client.ion({
        renderRows: me.onUIReady,
        thisObj: me,
        once: true
      });
    }
    me.cls = me.cls || `b-sch-${me.constructor.$$name.toLowerCase()}`;
    me.baseSelector = `.${me.baseCls}.${me.cls}`;
    if (me.enableResizing) {
      me.showHeaderElements = true;
    }
  }
  doDestroy() {
    var _a5, _b2, _c2, _d;
    const me = this;
    me.detachListeners("timeAxisViewModel");
    me.detachListeners("timeAxis");
    (_a5 = me.clockTemplate) == null ? void 0 : _a5.destroy();
    (_b2 = me.tip) == null ? void 0 : _b2.destroy();
    (_c2 = me.drag) == null ? void 0 : _c2.destroy();
    (_d = me.resize) == null ? void 0 : _d.destroy();
    super.doDestroy();
  }
  doDisable(disable) {
    if (!this.isConfiguring) {
      this.renderRanges();
    }
    super.doDisable(disable);
  }
  setupTimeAxisViewModelListeners() {
    const me = this;
    me.detachListeners("timeAxisViewModel");
    me.detachListeners("timeAxis");
    me.client.timeAxisViewModel.ion({
      name: "timeAxisViewModel",
      update: "onTimeAxisViewModelUpdate",
      thisObj: me
    });
    me.client.timeAxis.ion({
      name: "timeAxis",
      includeChange: "renderRanges",
      thisObj: me
    });
    me.updateLineBuffer();
  }
  onUIReady() {
    const me = this, { client } = me;
    client.ion({
      timeAxisViewModelChange: me.setupTimeAxisViewModelListeners,
      thisObj: me
    });
    me.setupTimeAxisViewModelListeners();
    if (!client.hideHeaders) {
      if (me.headerContainerElement) {
        EventHelper.on({
          click: me.onTimeRangeClick,
          dblclick: me.onTimeRangeClick,
          contextmenu: me.onTimeRangeClick,
          delegate: me.baseSelector,
          element: me.headerContainerElement,
          thisObj: me
        });
      }
      if (me.enableResizing) {
        me.drag = DragHelper.new({
          name: "rangeDrag",
          lockX: client.isVertical,
          lockY: client.isHorizontal,
          constrain: true,
          outerElement: me.headerContainerElement.parentElement,
          targetSelector: `${me.baseSelector}`,
          isElementDraggable: (el, event) => !client.readOnly && me.isElementDraggable(el, event),
          rtlSource: client,
          positioning: "inset",
          scrollManager: client.scrollManager,
          internalListeners: {
            dragStart: "onDragStart",
            drag: "onDrag",
            drop: "onDrop",
            reset: "onDragReset",
            abort: "onInvalidDrop",
            thisObj: me
          }
        }, me.dragHelperConfig);
        me.resize = ResizeHelper.new({
          direction: client.mode,
          targetSelector: `${me.baseSelector}.b-sch-range`,
          outerElement: me.headerContainerElement,
          isElementResizable: (el, event) => !el.matches(".b-dragging,.b-readonly") && !event.target.matches(".b-fa"),
          rtlSource: client,
          positioning: "inset",
          internalListeners: {
            resizeStart: "onResizeStart",
            resizing: "onResizeDrag",
            resize: "onResize",
            cancel: "onInvalidResize",
            reset: "onResizeReset",
            thisObj: me
          }
        }, me.resizeHelperConfig);
      }
    }
    me.renderRanges();
    if (me.tooltipTemplate) {
      me.hoverTooltip = new Tooltip(ObjectHelper.assign({
        forElement: me.headerContainerElement,
        getHtml({ activeTarget }) {
          const timeRange = me.resolveTimeRangeRecord(activeTarget);
          return me.tooltipTemplate({ timeRange });
        },
        forSelector: `.b-timelinebase:not(.b-dragging-timerange, .b-resizing-timerange) .${me.baseCls}${me.cls ? "." + me.cls : ""}`
      }, me.hoverTooltip));
    }
  }
  //endregion
  //region Draw
  refresh() {
    this._timeRanges = null;
    this.renderRanges();
  }
  getDOMConfig(startDate, endDate) {
    const me = this, bodyConfigs = [], headerConfigs = [];
    if (!me.disabled) {
      me._labelRotationMap = {};
      for (const range of me.timeRanges) {
        const result = me.renderRange(range, startDate, endDate);
        if (result) {
          bodyConfigs.push(result.bodyConfig);
          headerConfigs.push(result.headerConfig);
        }
      }
    }
    return [bodyConfigs, headerConfigs];
  }
  renderRanges() {
    const me = this, { client } = me;
    if (client.isPainted && !client.timeAxisSubGrid.collapsed) {
      const { headerContainerElement } = me, updatedBodyElements = [], [bodyConfigs, headerConfigs] = me.getDOMConfig();
      if (!me.bodyCanvas) {
        me.bodyCanvas = DomHelper.createElement({
          className: `b-timeranges-canvas b-timeranges-body-canvas ${me.cls}-canvas b-sch-canvas`,
          parent: client.timeAxisSubGridElement,
          retainElement: true
        });
      }
      DomSync.sync({
        targetElement: me.bodyCanvas,
        domConfig: {
          children: bodyConfigs,
          onlyChildren: true,
          syncOptions: {
            releaseThreshold: 0,
            syncIdField: "id"
          }
        },
        callback: me.showHeaderElements ? null : ({
          targetElement,
          action
        }) => {
          if (action === "reuseElement" || action === "newElement" || action === "reuseOwnElement") {
            updatedBodyElements.push(targetElement);
          }
        }
      });
      if (me.showHeaderElements && !me.headerCanvas) {
        me.headerCanvas = DomHelper.createElement({
          className: `b-timeranges-canvas b-timeranges-header-canvas ${me.cls}-canvas`,
          parent: headerContainerElement,
          retainElement: true
        });
      }
      if (me.headerCanvas) {
        DomSync.sync({
          targetElement: me.headerCanvas,
          domConfig: {
            onlyChildren: true,
            children: headerConfigs,
            syncOptions: {
              releaseThreshold: 0,
              syncIdField: "id"
            }
          }
        });
      }
      for (const bodyElement of updatedBodyElements) {
        me.cacheRotation(bodyElement.elementData.timeRange, bodyElement);
      }
      for (const bodyElement of updatedBodyElements) {
        me.applyRotation(bodyElement.elementData.timeRange, bodyElement);
      }
    }
  }
  // Implement in subclasses
  get timeRanges() {
    return [];
  }
  /**
   * Based on this method result the feature decides whether the provided range should
   * be rendered or not.
   * The method checks that the range intersects the current viewport.
   *
   * Override the method to implement your custom range rendering vetoing logic.
   * @param {Scheduler.model.TimeSpan} range Range to render.
   * @param {Date} [viewStartDate] Specifies view start date. Defaults to view visible range start
   * @param {Date} [viewEndDate] Specifies view end date. Defaults to view visible range end
   * @returns {Boolean} `true` if the range should be rendered and `false` otherwise.
   */
  shouldRenderRange(range, viewStartDate = this.client.visibleDateRange.startDate, viewEndDate = this.client.visibleDateRange.endDate) {
    const { timeAxis } = this.client, { startDate: rangeStart, endDate: rangeEnd, duration } = range;
    return Boolean(rangeStart && (timeAxis.isContinuous || timeAxis.isTimeSpanInAxis(range)) && DateHelper.intersectSpans(
      viewStartDate,
      viewEndDate,
      rangeStart,
      // Lines are included longer, to make sure label does not disappear
      duration ? rangeEnd : DateHelper.add(rangeStart, this._lineBufferDurationMS)
    ));
  }
  getRangeDomConfig(timeRange, minDate, maxDate, relativeTo = 0) {
    const me = this, { client } = me, { rtl, timeAxis } = client;
    let { startDate: timeRangeStart, endDate: timeRangeEnd } = timeRange;
    if (client.fillTicks && (timeRangeEnd && timeRangeEnd - timeRangeStart !== 0 || timeRange.fillAlign)) {
      const startTick = timeAxis.getSnappedTickFromDate(timeRangeStart);
      if (startTick) {
        timeRangeStart = timeRangeEnd || timeRange.fillAlign === "start" ? startTick.startDate : startTick.endDate;
      }
      if (timeRangeEnd) {
        const endTickIndex = Math.ceil(timeAxis.getTickFromDate(timeRangeEnd)) - 1, endTick = timeAxis.getAt(endTickIndex);
        if (endTick) {
          timeRangeEnd = endTick.endDate;
        }
      }
    }
    let startPos = client.getVirtualCoordFromDate(DateHelper.max(timeRangeStart, minDate), {
      respectExclusion: true
    }) - relativeTo;
    if (startPos === -1 && maxDate < timeRangeStart) {
      startPos = client.getVirtualCoordFromDate(maxDate);
    }
    const endPos = timeRangeEnd ? client.getVirtualCoordFromDate(DateHelper.min(timeRangeEnd, maxDate), {
      respectExclusion: true,
      isEnd: true
    }) - relativeTo : startPos, size = Math.abs(endPos - startPos), isRange = size > 0;
    return {
      className: {
        [me.baseCls]: 1,
        [me.cls]: me.cls,
        [me.rangeCls]: isRange,
        [me.lineCls]: !isRange,
        [timeRange.cls]: timeRange.cls,
        "b-narrow-range": isRange && size < me.narrowThreshold,
        "b-readonly": timeRange.readOnly,
        "b-rtl": rtl
      },
      dataset: {
        id: timeRange.id
      },
      elementData: {
        timeRange
      },
      style: client.isVertical ? `inset-block-start: ${startPos}px; ${isRange ? `height:${size}px` : ""};` : `inset-inline-start: ${startPos}px; ${isRange ? `width:${size}px` : ""};`
    };
  }
  renderRange(timeRange, startDate, endDate) {
    const me = this, { client } = me, { timeAxis } = client;
    if (client.timeAxisViewModel.availableSpace > 0 && timeAxis.startDate && me.shouldRenderRange(timeRange, startDate, endDate)) {
      const config = me.getRangeDomConfig(timeRange, timeAxis.startDate, timeAxis.endDate), icon = timeRange.iconCls && StringHelper.xss`<i class="${timeRange.iconCls}"></i>`, name = timeRange.name && StringHelper.encodeHtml(timeRange.name), labelTpl = name || icon ? `${icon || ""}<label>${name || "&nbsp;"}</label>` : "", bodyConfig = {
        ...config,
        style: config.style + (timeRange.style || ""),
        html: me.bodyRenderer ? me.bodyRenderer({ timeRange }) : me.showHeaderElements && !me.showLabelInBody ? "" : labelTpl
      };
      let headerConfig;
      if (me.showHeaderElements) {
        headerConfig = {
          ...config,
          html: me.headerRenderer ? me.headerRenderer({ timeRange }) : me.showLabelInBody ? "" : labelTpl
        };
      }
      return { bodyConfig, headerConfig };
    }
  }
  // Cache label rotation to not have to calculate for each occurrence when using recurring timeranges
  cacheRotation(range, bodyElement) {
    if (!range.iconCls && !range.name || !range.duration) {
      return;
    }
    const label = bodyElement.firstElementChild;
    if (label && !range.recurringTimeSpan) {
      this._labelRotationMap[range.id] = this.client.isVertical ? label.offsetHeight < bodyElement.offsetHeight : label.offsetWidth > bodyElement.offsetWidth;
    }
  }
  applyRotation(range, bodyElement) {
    var _a5, _b2, _c2;
    const rotate = this._labelRotationMap[(_b2 = (_a5 = range.recurringTimeSpan) == null ? void 0 : _a5.id) != null ? _b2 : range.id];
    (_c2 = bodyElement.firstElementChild) == null ? void 0 : _c2.classList.toggle("b-vertical", Boolean(rotate));
  }
  getBodyElementByRecord(idOrRecord) {
    const id2 = typeof idOrRecord === "string" ? idOrRecord : idOrRecord == null ? void 0 : idOrRecord.id;
    return id2 != null && DomSync.getChild(this.bodyCanvas, id2);
  }
  // Implement in subclasses
  resolveTimeRangeRecord(el) {
  }
  get headerContainerElement() {
    const me = this, { isVertical, timeAxisColumn } = me.client;
    if (!me._headerContainerElement) {
      if (isVertical) {
        me._headerContainerElement = me.client.subGrids.locked.element;
      } else if (!isVertical) {
        me._headerContainerElement = timeAxisColumn.element;
      }
    }
    return me._headerContainerElement;
  }
  //endregion
  //region Settings
  get showHeaderElements() {
    return !this.client.hideHeaders && this._showHeaderElements;
  }
  updateShowHeaderElements(show) {
    const { client } = this;
    if (!this.isConfiguring) {
      client.element.classList.toggle("b-sch-timeranges-with-headerelements", Boolean(show));
      this.renderRanges();
    }
  }
  //endregion
  //region Menu items
  /**
   * Adds menu items for the context menu, and may mutate the menu configuration.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Object<String,MenuItemConfig|Boolean|null>} options.items A named object to describe menu items
   * @internal
   */
  populateTimeAxisHeaderMenu({ column, items: items2 }) {
  }
  //endregion
  //region Events & hooks
  onInternalPaint({ firstPaint }) {
    if (firstPaint && this.client.isHorizontal) {
      this.onUIReady();
    }
  }
  onSchedulerHorizontalScroll() {
    this.client.isHorizontal && this.renderRanges();
  }
  afterScroll() {
    this.client.isVertical && this.renderRanges();
  }
  updateLineBuffer() {
    const { timeAxisViewModel } = this.client;
    this._lineBufferDurationMS = timeAxisViewModel.getDateFromPosition(300) - timeAxisViewModel.getDateFromPosition(0);
  }
  onInternalResize(element, newWidth, newHeight, oldWidth, oldHeight) {
    if (this.client.isVertical && oldHeight !== newHeight) {
      this.renderRanges();
    }
  }
  onTimeAxisViewModelUpdate() {
    this.updateLineBuffer();
    this.refresh();
  }
  onTimeRangeClick(event) {
    const timeRangeRecord = this.resolveTimeRangeRecord(event.target);
    this.client.trigger(`timeRangeHeader${StringHelper.capitalize(event.type)}`, { domEvent: event, timeRangeRecord });
  }
  //endregion
  //region Drag drop
  showTip(context) {
    const me = this;
    if (me.showTooltip) {
      me.clockTemplate = new ClockTemplate({
        scheduler: me.client
      });
      me.tip = Tooltip.reconfigure(me.tip, Tooltip.mergeConfigs({
        type: "tooltip",
        id: `${me.client.id}-time-range-tip`,
        cls: "b-interaction-tooltip",
        align: "b-t",
        autoShow: true,
        updateContentOnMouseMove: true,
        forElement: context.element,
        getHtml: () => me.getTipHtml(context.record, context.element)
      }, me.showTooltip));
    }
  }
  destroyTip() {
    if (this.tip) {
      this.tip.destroy();
      this.tip = null;
    }
  }
  isElementDraggable(el) {
    el = el.closest(this.baseSelector + ":not(.b-resizing):not(.b-readonly)");
    return el && !el.classList.contains("b-over-resize-handle");
  }
  onDragStart({ context }) {
    const { client, drag } = this;
    if (client.isVertical) {
      drag.minY = 0;
      drag.maxY = client.timeAxisViewModel.totalSize - context.element.offsetHeight;
      drag.minX = 0;
      drag.maxX = Number.MAX_SAFE_INTEGER;
    } else {
      drag.minX = 0;
      drag.maxX = client.timeAxisViewModel.totalSize - context.element.offsetWidth;
      drag.minY = 0;
      drag.maxY = Number.MAX_SAFE_INTEGER;
    }
    client.element.classList.add("b-dragging-timerange");
  }
  onDrop({ context }) {
    this.client.element.classList.remove("b-dragging-timerange");
  }
  onInvalidDrop() {
    this.drag.reset();
    this.client.element.classList.remove("b-dragging-timerange");
    this.destroyTip();
  }
  updateDateIndicator({ startDate, endDate }) {
    const me = this, { tip } = me, endDateElement = tip.element.querySelector(".b-sch-tooltip-enddate");
    me.clockTemplate.updateDateIndicator(tip.element, startDate);
    endDateElement && me.clockTemplate.updateDateIndicator(endDateElement, endDate);
  }
  onDrag({ context }) {
    const me = this;
    if (me.showTooltip) {
      const { client } = me, box = Rectangle.from(context.element), startPos = box.getStart(client.rtl, client.isHorizontal), endPos = box.getEnd(client.rtl, client.isHorizontal), startDate = client.getDateFromCoordinate(startPos, "round", false), endDate = client.getDateFromCoordinate(endPos, "round", false);
      me.updateDateIndicator({ startDate, endDate });
    }
  }
  onDragReset() {
  }
  // endregion
  // region Resize
  onResizeStart() {
    var _a5;
    this.client.element.classList.add("b-resizing-timerange");
    (_a5 = this.hoverTooltip) == null ? void 0 : _a5.hide();
  }
  onResizeDrag() {
  }
  onResize() {
  }
  onInvalidResize() {
  }
  onResizeReset() {
    this.client.element.classList.remove("b-resizing-timerange");
  }
  //endregion
  //region Tooltip
  /**
   * Generates the html to display in the tooltip during drag drop.
   *
   */
  getTipHtml(record, element) {
    const me = this, { client } = me, box = Rectangle.from(element), startPos = box.getStart(client.rtl, client.isHorizontal), endPos = box.getEnd(client.rtl, client.isHorizontal), startDate = client.getDateFromCoordinate(startPos, "round", false), endDate = record.endDate && client.getDateFromCoordinate(endPos, "round", false), startText = client.getFormattedDate(startDate), endText = endDate && client.getFormattedEndDate(endDate, startDate);
    return me.dragTipTemplate({
      name: record.name || "",
      startDate,
      endDate,
      startText,
      endText,
      startClockHtml: me.clockTemplate.template({
        date: startDate,
        text: startText,
        cls: "b-sch-tooltip-startdate"
      }),
      endClockHtml: endText && me.clockTemplate.template({
        date: endDate,
        text: endText,
        cls: "b-sch-tooltip-enddate"
      })
    });
  }
  //endregion
  updateFillTicks() {
    if (!this.isConfiguring) {
      this.refresh();
    }
  }
};
__publicField(AbstractTimeRanges, "configurable", {
  /**
   * Set to `false` to not render range elements into the time axis header
   * @prp {Boolean}
   * @default
   * @category Common
   */
  showHeaderElements: true
});
AbstractTimeRanges._$name = "AbstractTimeRanges";

// ../Scheduler/lib/Scheduler/feature/ColumnLines.js
var emptyObject15 = Object.freeze({});
var ColumnLines = class extends InstancePlugin.mixin(AttachToProjectMixin_default, Delayable_default) {
  static get delayable() {
    return {
      refresh: {
        type: "raf",
        cancelOutstanding: true
      }
    };
  }
  // Plugin configuration. This plugin chains some of the functions in Grid.
  static get pluginConfig() {
    return {
      after: ["render", "updateCanvasSize", "internalOnVisibleDateRangeChange", "onVisibleResourceRangeChange", "onVisibleResourceColumnChange"]
    };
  }
  //endregion
  //region Init & destroy
  attachToResourceStore(resourceStore) {
    const { client } = this;
    super.attachToResourceStore(resourceStore);
    if (client.isVertical) {
      client.resourceStore.ion({
        name: "resourceStore",
        group({ groupers }) {
          if (groupers.length === 0) {
            this.refresh();
          }
        },
        thisObj: this
      });
    }
  }
  doDisable(disable) {
    super.doDisable(disable);
    if (!this.isConfiguring) {
      this.refresh();
    }
  }
  //endregion
  //region Draw
  /**
   * Draw lines when scheduler/gantt is rendered.
   * @private
   */
  render() {
    this.refresh();
  }
  getColumnLinesDOMConfig(startDate, endDate, firstResource, lastResource) {
    const me = this, { client } = me, {
      timeAxisViewModel,
      isHorizontal,
      resourceStore,
      variableColumnWidths
    } = client, { columnConfig } = timeAxisViewModel;
    const linesForLevel = timeAxisViewModel.columnLinesFor, majorLinesForLevel = Math.max(linesForLevel - 1, 0), start = startDate.getTime(), end = endDate.getTime(), domConfigs = [], dates = /* @__PURE__ */ new Set(), dimension = isHorizontal ? "insetInlineStart" : "insetBlockStart";
    if (!me.disabled) {
      const addLineConfig = (tick, isMajor) => {
        const tickStart = tick.start.getTime();
        if (tickStart > start && tickStart < end && !dates.has(tickStart)) {
          const domConfig = {
            role: "presentation",
            className: {
              "b-column-line-major": isMajor,
              "b-column-line": !isMajor
            },
            style: {
              [dimension]: tick.coord
            },
            dataset: {
              line: isMajor ? `major-${tick.index}` : `line-${tick.index}`
            }
          };
          dates.add(tickStart);
          domConfigs.push(domConfig);
          if (me.renderer) {
            me.callback(me.renderer, me, [tick.start, domConfig]);
          }
        }
      };
      if (linesForLevel !== majorLinesForLevel) {
        for (let i = 1; i <= columnConfig[majorLinesForLevel].length - 1; i++) {
          addLineConfig(columnConfig[majorLinesForLevel][i], true);
        }
      }
      for (let i = 1; i <= columnConfig[linesForLevel].length - 1; i++) {
        addLineConfig(columnConfig[linesForLevel][i], false);
      }
      if (!isHorizontal && client.columnLines) {
        const { columnWidth } = client.resourceColumns;
        if (!firstResource && !lastResource) {
          ({
            first: firstResource,
            last: lastResource
          } = client.currentOrientation.getResourceRange(true));
        }
        let nbrGroupHeaders = 0;
        if (firstResource > -1) {
          for (let i = firstResource; i < lastResource + 1; i++) {
            const resourceRecord = resourceStore.getAt(i);
            if (resourceRecord.isGroupHeader) {
              lastResource++;
              nbrGroupHeaders++;
              continue;
            }
            const instanceMeta = resourceRecord.instanceMeta(client), left = variableColumnWidths ? instanceMeta.insetStart + client.getResourceWidth(resourceRecord) - 1 : (i - nbrGroupHeaders + 1) * columnWidth - 1, groupParent = resourceRecord.instanceMeta(client.resourceStore).groupParent;
            domConfigs.push({
              className: {
                "b-column-line": 1,
                "b-resource-column-line": 1,
                "b-resource-group-divider": resourceStore.isGrouped && (groupParent == null ? void 0 : groupParent.groupChildren[(groupParent == null ? void 0 : groupParent.groupChildren.length) - 1]) === resourceRecord
              },
              style: {
                insetInlineStart: left
              },
              dataset: {
                line: `resource-${i}`
              }
            });
          }
        }
      }
    }
    return domConfigs;
  }
  /**
   * Draw column lines that are in view
   * @private
   */
  refresh() {
    const me = this, { client } = me, { timeAxis } = client, { startDate, endDate } = client.visibleDateRange || emptyObject15, axisStart = timeAxis.startDate;
    if (!axisStart || !startDate || me.client.timeAxisSubGrid.collapsed) {
      return;
    }
    if (!me.element) {
      me.element = DomHelper.createElement({
        parent: client.timeAxisSubGridElement,
        className: "b-column-lines-canvas b-sch-canvas"
      });
    }
    const domConfigs = me.getColumnLinesDOMConfig(startDate, endDate);
    DomSync.sync({
      targetElement: me.element,
      domConfig: {
        onlyChildren: true,
        children: domConfigs,
        syncOptions: {
          // When zooming in and out we risk getting a lot of released lines if we do not limit it
          releaseThreshold: 4
        }
      },
      syncIdField: "line"
    });
  }
  //endregion
  //region Events
  // Called when visible date range changes, for example from zooming, scrolling, resizing
  internalOnVisibleDateRangeChange() {
    this.refresh.now();
  }
  // Called when visible resource range changes, for example on scroll and resize
  onVisibleResourceRangeChange({ firstResource, lastResource }) {
    this.refresh();
  }
  onVisibleResourceColumnChange(width, oldWidth) {
    this.refresh();
  }
  updateCanvasSize() {
    this.refresh();
  }
  //endregion
};
//region Config
__publicField(ColumnLines, "$name", "ColumnLines");
__publicField(ColumnLines, "configurable", {
  /**
   * A renderer function, or name of a function in the ownership hierarchy, called for each line, letting you mutate the DOM element through the passed
   * {@link DomConfig} object.
   *
   * ```javascript
   * scheduler = new Scheduler({
   *     width     : 1000,
   *     startDate : '2025-01-03',
   *     endDate   : '2025-01-09',
   *     features  : {
   *         columnLines : {
   *             renderer(date, domConfig) {
   *                 // Make Sunday line red
   *                 if (date.getDay() === 0) {
   *                     domConfig.style.borderColor = 'red';
   *                     domConfig.className.foo = 1;
   *                 }
   *             }
   *         }
   *     }
   * });
   * ```
   *
   * @param {Date} date The line date
   * @param {DomConfig} lineConfig The line DOM config object which you can mutate
   * @returns {void}
   * @config {Function|String}
   */
  renderer: null
});
ColumnLines._$name = "ColumnLines";
GridFeatureManager.registerFeature(ColumnLines, true, ["Scheduler", "Gantt", "TimelineHistogram"]);

// ../Scheduler/lib/Scheduler/feature/mixin/DependencyCreation.js
var DependencyCreation_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    static get defaultConfig() {
      return {
        /**
         * `false` to not show a tooltip while creating a dependency
         * @config {Boolean}
         * @default
         * @category Dependency creation
         */
        showCreationTooltip: true,
        /**
         * A tooltip config object that will be applied to the dependency creation {@link Core.widget.Tooltip}
         * @config {TooltipConfig}
         * @category Dependency creation
         */
        creationTooltip: null,
        /**
         * A template function that will be called to generate the HTML contents of the dependency creation tooltip.
         * You can return either an HTML string or a {@link DomConfig} object.
         * @prp {Function} creationTooltipTemplate
         * @param {Object} data Data about the dependency being created
         * @param {Scheduler.model.TimeSpan} data.source The from event
         * @param {Scheduler.model.TimeSpan} data.target The target event
         * @param {String} data.fromSide The from side (start, end, top, bottom)
         * @param {String} data.toSide The target side (start, end, top, bottom)
         * @param {Boolean} data.valid The validity of the dependency
         * @returns {String|DomConfig}
         * @category Dependency creation
         */
        /**
         * CSS class used for terminals
         * @config {String}
         * @default
         * @category Dependency terminals
         */
        terminalCls: "b-sch-terminal",
        /**
         * Where (on event bar edges) to display terminals. The sides are `'start'`, `'top'`,
         * `'end'` and `'bottom'`
         * @config {String[]}
         * @category Dependency terminals
         */
        terminalSides: ["start", "top", "end", "bottom"],
        /**
         * Set to `false` to not allow creating dependencies
         * @config {Boolean}
         * @default
         * @category Dependency creation
         */
        allowCreate: true
      };
    }
    //endregion
    //region Init & destroy
    construct(client, config) {
      super.construct(client, config);
      const me = this;
      me.eventName = client.scheduledEventName;
      client.ion({ readOnly: () => me.updateCreateListeners() });
      me.updateCreateListeners();
      me.chain(client, "onElementTouchMove", "onElementTouchMove");
    }
    doDestroy() {
      var _a6, _b2;
      const me = this;
      me.detachListeners("view");
      me.creationData = null;
      (_a6 = me.pointerUpMoveDetacher) == null ? void 0 : _a6.call(me);
      (_b2 = me.creationTooltip) == null ? void 0 : _b2.destroy();
      super.doDestroy();
    }
    updateCreateListeners() {
      const me = this;
      if (!me.client) {
        return;
      }
      me.detachListeners("view");
      if (me.isCreateAllowed) {
        me.client.ion({
          name: "view",
          [`${me.eventName}MouseEnter`]: "onTimeSpanMouseEnter",
          [`${me.eventName}MouseLeave`]: "onTimeSpanMouseLeave",
          thisObj: me
        });
      }
    }
    set allowCreate(value2) {
      this._allowCreate = value2;
      this.updateCreateListeners();
    }
    get allowCreate() {
      return this._allowCreate;
    }
    get isCreateAllowed() {
      return this.allowCreate && !this.client.readOnly && !this.disabled;
    }
    updateAllowCreateOnlyParent(value2) {
      this.client.element.classList.toggle("b-dependency-only-parent", value2);
    }
    //endregion
    //region Terminal settings
    updateTerminalOffset(offset) {
      this.client.whenVisible(() => {
        this.client.foregroundCanvas.style.setProperty("--scheduler-dependency-terminal-offset", `${-offset}px`);
      });
    }
    updateTerminalSize(size) {
      if (typeof size === "number") {
        size = `${size}px`;
      }
      this.client.whenVisible(() => {
        this.client.foregroundCanvas.style.setProperty("--scheduler-dependency-terminal-size", size ? `${size}` : null);
      });
    }
    //endregion
    //region Events
    /**
     * Show terminals when mouse enters event/task element
     * @private
     */
    onTimeSpanMouseEnter({
      event,
      source,
      [`${this.eventName}Record`]: record,
      [`${this.eventName}Element`]: element,
      resourceRecord
    }) {
      if (!record.isCreating && !record.readOnly) {
        const me = this, { creationData, client } = me, eventBarElement = DomHelper.down(element, source.eventInnerSelector);
        if (record !== (creationData == null ? void 0 : creationData.source)) {
          const { parent } = record;
          if (record.isEventModel && parent && !parent.isRoot && client.eventStore.includes(parent)) {
            const parentElement = client.getElementFromEventRecord(parent, resourceRecord, true);
            parentElement && me.delayHideTerminals(parentElement);
          }
          me.delayShowTerminals(record, element);
          if (creationData && event.target.closest(client.eventSelector)) {
            creationData.timeSpanElement = eventBarElement;
            me.onOverTargetEventBar(event);
          }
        }
      }
    }
    /**
     * Hide terminals when mouse leaves event/task element
     * @private
     */
    onTimeSpanMouseLeave(event) {
      var _a6, _b2, _c2, _d, _e;
      const { eventRecord, event: domEvent } = event;
      if (!domEvent) {
        return;
      }
      const me = this, { creationData, client } = me, toEvent = ((_a6 = domEvent.relatedTarget) == null ? void 0 : _a6.matches(".b-sch-terminal-hover-area, .b-sch-terminal")) ? null : (_b2 = domEvent.relatedTarget) == null ? void 0 : _b2.closest(client.eventSelector), toEventRecord = (_e = (_c2 = toEvent == null ? void 0 : toEvent.elementData) == null ? void 0 : _c2.eventRecord) != null ? _e : (_d = toEvent == null ? void 0 : toEvent.elementData) == null ? void 0 : _d.taskRecord, { parent } = eventRecord != null ? eventRecord : {}, element = event[`${me.eventName}Element`];
      if (domEvent.isTrusted || VersionHelper.isTestEnv || creationData) {
        me.delayHideTerminals(element);
      }
      if (parent && !parent.isRoot && client.eventStore.includes(parent)) {
        const parentElement = client.getElementFromEventRecord(parent);
        parentElement && me.delayShowTerminals(parent, parentElement);
      }
      if (creationData) {
        me.onOverNewTargetWhileCreating(domEvent.relatedTarget, !(toEventRecord == null ? void 0 : toEventRecord.readOnly) && !(toEventRecord == null ? void 0 : toEventRecord.isOccurence) ? toEventRecord : null, domEvent);
      }
    }
    onTerminalMouseOver(event) {
      this.clearTimeout(`hide-${event.target.closest(this.client.eventSelector).dataset.syncId}`);
      if (this.creationData) {
        this.onOverTargetEventBar(event);
      }
    }
    /**
     * Remove hover styling when mouse leaves terminal. Also hides terminals when mouse leaves one it and not creating a
     * dependency.
     * @private
     */
    onTerminalMouseOut(event) {
      var _a6, _b2, _c2, _d;
      const me = this, { creationData, client } = me, fromEvent = event.target.closest(client.eventSelector), toEvent = (_a6 = event.relatedTarget) == null ? void 0 : _a6.closest(client.eventSelector), toEventRecord = (_d = (_b2 = toEvent == null ? void 0 : toEvent.elementData) == null ? void 0 : _b2.eventRecord) != null ? _d : (_c2 = toEvent == null ? void 0 : toEvent.elementData) == null ? void 0 : _c2.taskRecord;
      if (toEvent !== fromEvent && fromEvent && !me.hasTimeout(`show-${fromEvent.dataset.syncId}`) && (!creationData || fromEvent !== creationData.timeSpanElement)) {
        me.delayHideTerminals(fromEvent);
        client.unhover(fromEvent, event);
      }
      if (creationData) {
        me.onOverNewTargetWhileCreating(event.relatedTarget, !(toEventRecord == null ? void 0 : toEventRecord.readOnly) && !(toEventRecord == null ? void 0 : toEventRecord.isOccurence) ? toEventRecord : null, event);
      }
    }
    /**
     * Start creating a dependency when mouse is pressed over terminal
     * @private
     */
    onTerminalPointerDown(event) {
      var _a6;
      const me = this;
      if (event.button === 0 && !me.creationData) {
        const { client } = me, timeAxisSubGridElement = client.timeAxisSubGridElement, terminalNode = event.target, timeSpanElement = terminalNode.closest(client.eventInnerSelector), viewBounds = Rectangle.from(client.element);
        event.stopPropagation();
        me.creationData = {
          sourceElement: timeSpanElement,
          source: client.resolveTimeSpanRecord(timeSpanElement).$original,
          fromSide: terminalNode.dataset.side,
          startPoint: Rectangle.from(terminalNode, timeAxisSubGridElement).center,
          startX: event.pageX - viewBounds.x + client.scrollLeft,
          startY: event.pageY - viewBounds.y + client.scrollTop,
          valid: false,
          sourceResource: (_a6 = client.resolveResourceRecord) == null ? void 0 : _a6.call(client, event),
          tooltip: me.creationTooltip
        };
        me.pointerUpMoveDetacher = EventHelper.on({
          pointerup: {
            element: DomHelper.getRootElement(client.element),
            handler: "onMouseUp",
            passive: false
          },
          pointermove: {
            element: timeAxisSubGridElement,
            handler: "onMouseMove",
            passive: false
          },
          thisObj: me
        });
        me.documentPointerUpDetacher = EventHelper.on({
          pointerup: {
            element: document,
            handler: "onDocumentMouseUp"
          },
          keydown: {
            element: document,
            handler: ({ key }) => {
              if (key === "Escape") {
                me.abort();
              }
            }
          },
          thisObj: me
        });
      }
    }
    onElementTouchMove(event) {
      var _a6;
      (_a6 = super.onElementTouchMove) == null ? void 0 : _a6.call(this, event);
      if (this.connector) {
        event.preventDefault();
      }
    }
    /**
     * Update connector line showing dependency between source and target when mouse moves. Also check if mouse is over
     * a valid target terminal
     * @private
     */
    onMouseMove(event) {
      const me = this, { client, creationData: data } = me, viewBounds = Rectangle.from(client.element), deltaX = event.pageX - viewBounds.x + client.scrollLeft - data.startX, deltaY = event.pageY - viewBounds.y + client.scrollTop - data.startY, length = Math.round(Math.sqrt(deltaX * deltaX + deltaY * deltaY)) - 3, angle = Math.atan2(deltaY, deltaX);
      let { connector } = me;
      if (!connector) {
        if (me.onRequestDragCreate(event) === false) {
          me.abort();
          return;
        }
        connector = me.connector;
      }
      connector.style.width = `${length}px`;
      connector.style.transform = `rotate(${angle}rad)`;
      me.lastMouseMoveEvent = event;
    }
    onRequestDragCreate(event) {
      const me = this, { client, creationData: data } = me;
      if (client.trigger("beforeDependencyCreateDrag", { data, source: data.source }) === false) {
        return false;
      }
      client.element.classList.add("b-creating-dependency");
      me.createConnector(data.startPoint.x, data.startPoint.y);
      client.trigger("dependencyCreateDragStart", { data, source: data.source });
      if (me.showCreationTooltip) {
        const tip = me.creationTooltip || (me.creationTooltip = me.createDragTooltip());
        me.creationData.tooltip = tip;
        tip.disabled = false;
        tip.show();
        tip.onMouseMove(event);
      }
      client.scrollManager.startMonitoring({
        scrollables: [
          {
            element: client.timeAxisSubGrid.scrollable.element,
            direction: "horizontal"
          },
          {
            element: client.scrollable.element,
            direction: "vertical"
          }
        ],
        callback: () => me.lastMouseMoveEvent && me.onMouseMove(me.lastMouseMoveEvent)
      });
    }
    onOverTargetEventBar(event) {
      const me = this, { client, creationData: data, allowDropOnEventBar } = me, { target } = event;
      let overEventRecord = client.resolveTimeSpanRecord(target).$original;
      if (overEventRecord == null ? void 0 : overEventRecord.isEventSegment) {
        overEventRecord = overEventRecord.event;
      }
      if (Objects.isPromise(data.valid) || !allowDropOnEventBar && !target.classList.contains(me.terminalCls)) {
        return;
      }
      if (overEventRecord !== data.source) {
        me.onOverNewTargetWhileCreating(target, overEventRecord, event);
      }
    }
    async onOverNewTargetWhileCreating(targetElement, overEventRecord, event) {
      var _a6, _b2;
      const me = this, { client, creationData: data, allowDropOnEventBar, connector } = me;
      if (Objects.isPromise(data.valid)) {
        return;
      }
      if (data.finalizing) {
        return;
      }
      if (!connector) {
        return;
      }
      connector.classList.remove("b-valid", "b-invalid");
      data.timeSpanElement && DomHelper.removeClsGlobally(data.timeSpanElement, "b-sch-terminal-active");
      if (!overEventRecord || overEventRecord === data.source || !allowDropOnEventBar && !targetElement.classList.contains(me.terminalCls) || me.allowCreateOnlyParent && !overEventRecord.isParent) {
        data.target = data.toSide = null;
        data.valid = false;
        connector.classList.add("b-invalid");
      } else {
        const target = data.target = overEventRecord, { source } = data;
        let toSide = targetElement.dataset.side;
        if (allowDropOnEventBar && !targetElement.classList.contains(me.terminalCls)) {
          toSide = me.getTargetSideFromType(me.dependencyStore.modelClass.fieldMap.type.defaultValue || DependencyBaseModel.Type.EndToStart);
        }
        if (client.resolveResourceRecord) {
          data.targetResource = client.resolveResourceRecord(event);
        }
        let dependencyType;
        data.toSide = toSide;
        const fromSide = data.fromSide, updateValidity = (valid2) => {
          if (!me.isDestroyed) {
            data.valid = valid2;
            targetElement.classList.add(valid2 ? "b-valid" : "b-invalid");
            connector.classList.add(valid2 ? "b-valid" : "b-invalid");
            client.trigger("dependencyValidationComplete", {
              data,
              source,
              target,
              dependencyType
            });
          }
        };
        switch (true) {
          case (fromSide === "start" && toSide === "start"):
            dependencyType = DependencyBaseModel.Type.StartToStart;
            break;
          case (fromSide === "start" && toSide === "end"):
            dependencyType = DependencyBaseModel.Type.StartToEnd;
            break;
          case (fromSide === "end" && toSide === "start"):
            dependencyType = DependencyBaseModel.Type.EndToStart;
            break;
          case (fromSide === "end" && toSide === "end"):
            dependencyType = DependencyBaseModel.Type.EndToEnd;
            break;
        }
        client.trigger("dependencyValidationStart", {
          data,
          source,
          target,
          dependencyType
        });
        let valid = data.valid = me.dependencyStore.isValidDependency(source, target, dependencyType);
        if (Objects.isPromise(valid)) {
          valid = await valid;
          updateValidity(valid);
        } else {
          updateValidity(valid);
        }
        const validityCls = valid ? "b-valid" : "b-invalid";
        connector.classList.add(validityCls);
        (_b2 = (_a6 = data.timeSpanElement) == null ? void 0 : _a6.querySelector(`.${me.terminalCls}[data-side=${toSide}]`)) == null ? void 0 : _b2.classList.add("b-sch-terminal-active", validityCls);
      }
      me.updateCreationTooltip();
    }
    /**
     * Create a new dependency if mouse release over valid terminal. Hides connector
     * @private
     */
    async onMouseUp() {
      var _a6;
      const me = this, data = me.creationData;
      data.finalizing = true;
      (_a6 = me.pointerUpMoveDetacher) == null ? void 0 : _a6.call(me);
      if (data.valid) {
        const result = await me.client.trigger("beforeDependencyCreateFinalize", data);
        if (result === false) {
          data.valid = false;
        } else if (Objects.isPromise(data.valid)) {
          data.valid = await data.valid;
        }
        if (data.valid) {
          let dependency = await me.createDependency(data);
          if (dependency !== null) {
            if (Objects.isPromise(dependency)) {
              dependency = await dependency;
            }
            data.dependency = dependency;
            me.client.trigger("dependencyCreateDrop", { data, source: data.source, target: data.target, dependency });
            await me.doAfterDependencyDrop(data);
          }
        } else {
          await me.doAfterDependencyDrop(data);
        }
      } else {
        data.valid = false;
        await me.doAfterDependencyDrop(data);
      }
      me.abort();
    }
    doAfterDependencyDrop(data) {
      this.client.trigger("afterDependencyCreateDrop", {
        data,
        ...data
      });
    }
    onDocumentMouseUp({ target }) {
      if (!this.client.timeAxisSubGridElement.contains(target)) {
        this.abort();
      }
    }
    /**
     * Aborts dependency creation, removes proxy and cleans up listeners
     * @category Dependency creation
     */
    abort() {
      var _a6, _b2;
      const me = this, { client, creationData } = me;
      if (creationData) {
        const { source, sourceResource, target, targetResource } = creationData;
        if (source) {
          const el = client.getElementFromEventRecord(source, sourceResource);
          if (el) {
            me.hideTerminals(el);
          }
        }
        if (target) {
          const el = client.getElementFromEventRecord(target, targetResource);
          if (el) {
            me.hideTerminals(el);
          }
        }
      }
      if (me.creationTooltip) {
        me.creationTooltip.disabled = true;
      }
      me.creationData = me.lastMouseMoveEvent = null;
      (_a6 = me.pointerUpMoveDetacher) == null ? void 0 : _a6.call(me);
      (_b2 = me.documentPointerUpDetacher) == null ? void 0 : _b2.call(me);
      me.removeConnector();
    }
    //endregion
    //region Connector
    /**
     * Creates a connector line that visualizes dependency source & target
     * @private
     */
    createConnector(x, y) {
      const me = this, { client } = me;
      me.clearTimeout(me.removeConnectorTimeout);
      me.connector = DomHelper.createElement({
        parent: client.timeAxisSubGridElement,
        className: `${me.baseCls}-connector`,
        style: `left:${x}px;top:${y}px`
      });
      client.element.classList.add("b-creating-dependency");
    }
    createDragTooltip() {
      const me = this, { client } = me;
      return me.creationTooltip = Tooltip.new({
        id: `${client.id}-dependency-drag-tip`,
        cls: "b-sch-dependency-creation-tooltip",
        loadingMsg: "",
        anchorToTarget: false,
        // Keep tip visible until drag drop operation is finalized
        forElement: client.timeAxisSubGridElement,
        trackMouse: true,
        // Do not constrain at all, want it to be able to go outside of the viewport to not get in the way
        constrainTo: null,
        header: {
          dock: "right"
        },
        internalListeners: {
          // Show initial content immediately
          beforeShow: "updateCreationTooltip",
          thisObj: me
        }
      }, me.creationTooltip);
    }
    /**
     * Remove connector
     * @private
     */
    removeConnector() {
      const me = this, { connector, client } = me;
      if (connector) {
        connector.classList.add("b-removing");
        connector.style.width = "0";
        me.removeConnectorTimeout = me.setTimeout(() => {
          connector.remove();
          me.connector = null;
        }, 200);
      }
      client.element.classList.remove("b-creating-dependency");
      me.creationTooltip && me.creationTooltip.hide();
      client.scrollManager.stopMonitoring();
    }
    //endregion
    //region Terminals
    delayShowTerminals(timeSpanRecord, element) {
      const me = this, { syncId } = element.dataset;
      me.clearTimeout(`hide-${syncId}`);
      me.clearTimeout(`show-${syncId}`);
      element.classList.remove("b-hiding-terminals");
      if (!me.terminalShowDelay) {
        me.showTerminals(timeSpanRecord, element);
      } else {
        me.setTimeout({
          fn: () => me.showTerminals(timeSpanRecord, element),
          name: `show-${syncId}`,
          args: [timeSpanRecord, element],
          delay: me.terminalShowDelay
        });
      }
    }
    delayHideTerminals(element) {
      const me = this, { syncId } = element.dataset;
      me.clearTimeout(`hide-${syncId}`);
      me.clearTimeout(`show-${syncId}`);
      element.classList.add("b-hiding-terminals");
      if (!me.terminalHideDelay) {
        me.hideTerminals(element);
      } else {
        me.setTimeout({
          fn: () => me.hideTerminals(element),
          name: `hide-${syncId}`,
          args: [element],
          delay: me.terminalHideDelay
        });
      }
    }
    /**
     * Show terminals for specified event at sides defined in #terminalSides.
     * @param {Scheduler.model.TimeSpan} timeSpanRecord Event/task to show terminals for
     * @param {HTMLElement} [element] Event/task element, defaults to using the first element found for the task
     * @category Dependency creation
     */
    showTerminals(timeSpanRecord, element = this.client.getElementFromEventRecord(timeSpanRecord)) {
      const me = this;
      if (!me.isCreateAllowed || !timeSpanRecord.project || !element || me.allowCreateOnlyParent && !timeSpanRecord.isParent) {
        return;
      }
      const { client } = me, cls = me.terminalCls, terminalsVisibleCls = `${cls}s-visible`;
      if (me.showingTerminalsFor) {
        me.hideTerminals(me.showingTerminalsFor);
      }
      element = DomHelper.down(element, me.client.eventInnerSelector);
      if (!element.classList.contains(terminalsVisibleCls) && !client.element.classList.contains("b-resizing-event") && !client.readOnly) {
        if (client.trigger("beforeShowTerminals", { source: timeSpanRecord }) === false) {
          return;
        }
        DomHelper.createElement({
          parent: element.closest(client.eventSelector),
          className: "b-sch-terminal-hover-area"
        });
        me.terminalSides.forEach((side) => {
          side = me.fixSide(side);
          const terminal = DomHelper.createElement({
            parent: element,
            className: `${cls} ${cls}-${side}`,
            dataset: {
              side,
              feature: true
            }
          });
          terminal.detacher = EventHelper.on({
            element: terminal,
            mouseover: "onTerminalMouseOver",
            mouseout: "onTerminalMouseOut",
            // Needs to be pointerdown to match DragHelper, otherwise will be preventing wrong event
            pointerdown: {
              handler: "onTerminalPointerDown",
              capture: true
            },
            thisObj: me
          });
        });
        element.classList.add(terminalsVisibleCls);
        timeSpanRecord.internalCls.add(terminalsVisibleCls);
      }
    }
    fixSide(side) {
      if (side === "left") {
        return "start";
      }
      if (side === "right") {
        return "end";
      }
      return side;
    }
    /**
     * Hide terminals for specified event
     * @param {HTMLElement} eventElement Event element
     * @category Dependency creation
     */
    hideTerminals(eventElement) {
      var _a6;
      const me = this, { client } = me, eventParams = client.getTimeSpanMouseEventParams(eventElement), timeSpanRecord = eventParams == null ? void 0 : eventParams[`${me.eventName}Record`], terminalsVisibleCls = `${me.terminalCls}s-visible`;
      (_a6 = eventElement.querySelector(".b-sch-terminal-hover-area")) == null ? void 0 : _a6.remove();
      DomHelper.forEachSelector(eventElement, `> ${client.eventInnerSelector} > .${me.terminalCls}`, (terminal) => {
        var _a7;
        (_a7 = terminal.detacher) == null ? void 0 : _a7.call(terminal);
        terminal.remove();
      });
      DomHelper.down(eventElement, client.eventInnerSelector).classList.remove(terminalsVisibleCls);
      timeSpanRecord == null ? void 0 : timeSpanRecord.internalCls.remove(terminalsVisibleCls);
      eventElement.classList.remove("b-hiding-terminals");
    }
    //endregion
    //region Dependency creation
    /**
     * Create a new dependency from source terminal to target terminal
     * @internal
     */
    createDependency(data) {
      const { source, target, fromSide, toSide } = data, type = (fromSide === "start" ? 0 : 2) + (toSide === "end" ? 1 : 0);
      const newDependency = this.dependencyStore.add({
        from: source.id,
        to: target.id,
        type,
        fromSide,
        toSide
      });
      return newDependency !== null ? newDependency[0] : null;
    }
    getTargetSideFromType(type) {
      if (type === DependencyBaseModel.Type.StartToStart || type === DependencyBaseModel.Type.EndToStart) {
        return "start";
      }
      return "end";
    }
    //endregion
    //region Tooltip
    /**
     * Update dependency creation tooltip
     * @private
     */
    updateCreationTooltip() {
      const me = this;
      if (!me.showCreationTooltip) {
        return;
      }
      const data = me.creationData, { valid } = data, tip = me.creationTooltip, { classList } = tip.element;
      if (Objects.isPromise(valid)) {
        classList.remove("b-invalid");
        classList.add("b-checking");
        return new Promise((resolve) => valid.then((valid2) => {
          data.valid = valid2;
          if (!tip.isDestroyed) {
            resolve(me.updateCreationTooltip());
          }
        }));
      }
      tip.html = me.creationTooltipTemplate(data);
    }
    creationTooltipTemplate(data) {
      var _a6, _b2;
      const me = this, { tooltip, valid } = data, { classList } = tooltip.element;
      Object.assign(data, {
        fromText: data.source.name,
        toText: (_b2 = (_a6 = data.target) == null ? void 0 : _a6.name) != null ? _b2 : "",
        fromSide: data.fromSide,
        toSide: data.toSide || ""
      });
      let tipTitleIconClsSuffix, tipTitleText;
      classList.toggle("b-invalid", !valid);
      classList.remove("b-checking");
      if (valid === true) {
        tipTitleIconClsSuffix = "valid";
        tipTitleText = me.L("L{Dependencies.valid}");
      } else {
        tipTitleIconClsSuffix = "invalid";
        tipTitleText = me.L("L{Dependencies.invalid}");
      }
      tooltip.title = `<i class="b-icon b-icon-${tipTitleIconClsSuffix}"></i>${tipTitleText}`;
      return [
        { dataset: { ref: "fromLabel" }, tag: "label", text: me.L("L{Dependencies.from}") },
        { dataset: { ref: "fromText" }, text: data.fromText },
        { dataset: { ref: "fromBox" }, className: `b-sch-box b-${data.fromSide}` },
        { dataset: { ref: "toLabel" }, tag: "label", text: me.L("L{Dependencies.to}") },
        { dataset: { ref: "toText" }, text: data.toText },
        { dataset: { ref: "toBox" }, className: `b-sch-box b-${data.toSide}` }
      ];
    }
    //endregion
    doDisable(disable) {
      if (!this.isConfiguring) {
        this.updateCreateListeners();
      }
      super.doDisable(disable);
    }
  }, __publicField(_a5, "$name", "DependencyCreation"), //region Config
  __publicField(_a5, "configurable", {
    /**
     * `false` to require a drop on a target event bar side circle to define the dependency type.
     * If dropped on the event bar, the `defaultValue` of the DependencyModel `type` field will be used to
     * determine the target task side.
     *
     * @prp {Boolean}
     * @default
     * @category Dependency creation
     */
    allowDropOnEventBar: true,
    /**
     * Terminal diameter in px, overrides the default CSS value for it (which might depend on theme).
     *
     * {@note}
     * Use an even number to avoid cropped terminals.
     * {/@note}
     *
     * Also accepts a string value representing a CSS size, e.g. '1.5em'.
     *
     * @prp {Number|String}
     * @category Dependency terminals
     */
    terminalSize: null,
    /**
     * Terminal offset from their initial position, in px. Positive values move terminals further away from the
     * event bar, negative values inside the event bar.
     *
     * @prp {Number}
     * @default 0
     * @category Dependency terminals
     */
    terminalOffset: null,
    /**
     * Delay in ms before showing the terminals when hovering over an event bar.
     *
     * Can be used for a more "stable" UI, where the terminals are not shown immediately when hovering over an event
     * bar and thus have fewer things moving when mouse is moved quickly over multiple event bars.
     *
     * @prp {Number}
     * @default 0
     * @category Dependency terminals
     */
    terminalShowDelay: 0,
    /**
     * Delay in ms before hiding the terminals when the mouse leaves an event bar or terminal.
     *
     * Can be used to make the UI more forgiving, accidentally leaving the event bar or terminal will not
     * immediately hide the terminals.
     *
     * Can also be used to play a hide animation, set a `terminalHideDelay` that is longer than your animation's
     * duration. The `b-hiding-terminals` CSS class is added to the event wrapper while the terminals are being
     * hidden.
     *
     * @prp {Number}
     * @default 0
     * @category Dependency terminals
     */
    terminalHideDelay: 0,
    /**
     * Set it to `true` to allow dependency creation only for parent events (only applies to Scheduler Pro using the `NestedEvents` feature). Normally the nested event container
     * inside parent events cannot be scrolled when using dependencies, but by enabling this setting and limiting
     * to where dependencies can be drawn scrolling will be enabled.
     * @config {Boolean}
     * @default
     * @category Dependency creation
     */
    allowCreateOnlyParent: false
  }), _a5;
};

// ../Scheduler/lib/Scheduler/feature/mixin/DependencyGridCache.js
var ROWS_PER_CELL = 25;
var DependencyGridCache_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    constructor() {
      super(...arguments);
      __publicField(this, "gridCache", null);
    }
    // Dependencies that might intersect the current viewport and thus should be considered for drawing
    getDependenciesToConsider(startMS, endMS, startIndex, endIndex) {
      const me = this, { gridCache } = me, { timeAxis } = me.client;
      if (gridCache) {
        const dependencies = /* @__PURE__ */ new Set(), fromMSCell = Math.floor((startMS - timeAxis.startMS) / me.MS_PER_CELL), toMSCell = Math.floor((endMS - timeAxis.startMS) / me.MS_PER_CELL), fromRowCell = Math.floor(startIndex / ROWS_PER_CELL), toRowCell = Math.floor(endIndex / ROWS_PER_CELL);
        for (let i = fromMSCell; i <= toMSCell; i++) {
          const msCell = gridCache[i];
          if (msCell) {
            for (let j = fromRowCell; j <= toRowCell; j++) {
              const intersectingDependencies = msCell[j];
              if (intersectingDependencies) {
                for (let i2 = 0; i2 < intersectingDependencies.length; i2++) {
                  dependencies.add(intersectingDependencies[i2]);
                }
              }
            }
          }
        }
        return dependencies;
      }
    }
    // A (single) dependency was drawn, we might want to store info about it in the grid cache
    afterDrawDependency(dependency, fromIndex, toIndex, fromDateMS, toDateMS) {
      var _a6, _b2;
      const me = this;
      if (me.constructGridCache) {
        const { MS_PER_CELL } = me, {
          startMS: timeAxisStartMS,
          endMS: timeAxisEndMS
        } = me.client.timeAxis, timeAxisCells = Math.ceil((timeAxisEndMS - timeAxisStartMS) / MS_PER_CELL), fromMSCell = Math.floor((fromDateMS - timeAxisStartMS) / MS_PER_CELL), toMSCell = Math.floor((toDateMS - timeAxisStartMS) / MS_PER_CELL), fromRowCell = Math.floor(fromIndex / ROWS_PER_CELL), toRowCell = Math.floor(toIndex / ROWS_PER_CELL), firstMSCell = Math.min(fromMSCell, toMSCell), lastMSCell = Math.max(fromMSCell, toMSCell), firstRowCell = Math.min(fromRowCell, toRowCell), lastRowCell = Math.max(fromRowCell, toRowCell);
        if (firstMSCell < 0 && lastMSCell < 0 || firstMSCell > timeAxisCells && lastMSCell > timeAxisCells) {
          return;
        }
        const startMSCell = Math.max(firstMSCell, 0), endMSCell = Math.min(lastMSCell, timeAxisCells);
        for (let i = startMSCell; i <= endMSCell; i++) {
          const msCell = (_a6 = me.gridCache[i]) != null ? _a6 : me.gridCache[i] = {};
          for (let j = firstRowCell; j <= lastRowCell; j++) {
            const rowCell = (_b2 = msCell[j]) != null ? _b2 : msCell[j] = [];
            rowCell.push(dependency);
          }
        }
      }
    }
    // All dependencies are about to be drawn, check if we need to build the grid cache
    beforeDraw() {
      const me = this;
      if (!me.gridCache) {
        const { visibleDateRange } = me.client;
        me.constructGridCache = true;
        me.MS_PER_CELL = Math.max(visibleDateRange.endMS - visibleDateRange.startMS, 1e3);
        me.gridCache = {};
      }
    }
    // All dependencies are drawn, we no longer need to rebuild the cache
    afterDraw() {
      this.constructGridCache = false;
    }
    reset() {
      this.gridCache = null;
    }
  }, __publicField(_a5, "$name", "DependencyGridCache"), _a5;
};

// ../Scheduler/lib/Scheduler/util/RectangularPathFinder.js
var THRESHOLD = Math.min(1 / globalThis.devicePixelRatio, 0.75);
var BOX_PROPERTIES = ["start", "end", "top", "bottom"];
var equalEnough = (a, b) => Math.abs(a - b) < 0.1;
var sideToSide = {
  l: "left",
  r: "right",
  t: "top",
  b: "bottom"
};
var RectangularPathFinder = class _RectangularPathFinder extends Base {
  static get configurable() {
    return {
      /**
       * Default start connection side: 'left', 'right', 'top', 'bottom'
       * @config {'top'|'bottom'|'left'|'right'}
       * @default
       */
      startSide: "right",
      // /**
      //  * Default start arrow size in pixels
      //  * @config {Number}
      //  * @default
      //  */
      // startArrowSize : 0,
      /**
       * Default start arrow staff size in pixels
       * @config {Number}
       * @default
       */
      startArrowMargin: 12,
      /**
       * Default starting connection point shift from box's arrow pointing side middle point
       * @config {Number}
       * @default
       */
      startShift: 0,
      /**
       * Default end arrow pointing direction, possible values are: 'left', 'right', 'top', 'bottom'
       * @config {'top'|'bottom'|'left'|'right'}
       * @default
       */
      endSide: "left",
      // /**
      //  * Default end arrow size in pixels
      //  * @config {Number}
      //  * @default
      //  */
      // endArrowSize : 0,
      /**
       * Default end arrow staff size in pixels
       * @config {Number}
       * @default
       */
      endArrowMargin: 12,
      /**
       * Default ending connection point shift from box's arrow pointing side middle point
       * @config {Number}
       * @default
       */
      endShift: 0,
      /**
       * Start / End box vertical margin, the amount of pixels from top and bottom line of a box where drawing
       * is prohibited
       * @config {Number}
       * @default
       */
      verticalMargin: 2,
      /**
       * Start / End box horizontal margin, the amount of pixels from left and right line of a box where drawing
       * @config {Number}
       * @default
       */
      horizontalMargin: 5,
      /**
       * Other rectangular areas (obstacles) to search path through
       * @config {Object[]}
       * @default
       */
      otherBoxes: null,
      /**
       * The owning Scheduler. Mandatory so that it can determin RTL state.
       * @config {Scheduler.view.Scheduler}
       * @private
       */
      client: {}
    };
  }
  /**
   * Returns list of horizontal and vertical segments connecting two boxes
   * <pre>
   *    |    | |  |    |       |
   *  --+----+----+----*-------*---
   *  --+=>Start  +----*-------*--
   *  --+----+----+----*-------*--
   *    |    | |  |    |       |
   *    |    | |  |    |       |
   *  --*----*-+-------+-------+--
   *  --*----*-+         End <=+--
   *  --*----*-+-------+-------+--
   *    |    | |  |    |       |
   * </pre>
   * Path goes by lines (-=) and turns at intersections (+), boxes depicted are adjusted by horizontal/vertical
   * margin and arrow margin, original boxes are smaller (path can't go at original box borders). Algorithm finds
   * the shortest path with minimum amount of turns. In short it's mix of "Lee" and "Dijkstra pathfinding"
   * with turns amount taken into account for distance calculation.
   *
   * The algorithm is not very performant though, it's O(N^2), where N is amount of
   * points in the grid, but since the maximum amount of points in the grid might be up to 34 (not 36 since
   * two box middle points are not permitted) that might be ok for now.
   *
   * @param {Object} lineDef An object containing any of the class configuration option overrides as well
   *                         as `startBox`, `endBox`, `startHorizontalMargin`, `startVerticalMargin`,
   *                         `endHorizontalMargin`, `endVerticalMargin` properties
   * @param {Object} lineDef.startBox An object containing `start`, `end`, `top`, `bottom` properties
   * @param {Object} lineDef.endBox   An object containing `start`, `end`, `top`, `bottom` properties
   * @param {Number} lineDef.startHorizontalMargin Horizontal margin override for start box
   * @param {Number} lineDef.startVerticalMargin   Vertical margin override for start box
   * @param {Number} lineDef.endHorizontalMargin   Horizontal margin override for end box
   * @param {Number} lineDef.endVerticalMargin     Vertical margin override for end box
   * @returns {Number[]|Boolean} Array of line segments or `false` if path cannot be found
   */
  findPath(lineDef, noPathFallbackFn) {
    const me = this, originalLineDef = lineDef;
    let lineDefFull, startBox, endBox, startShift, endShift, startSide, endSide, startArrowMargin, endArrowMargin, horizontalMargin, verticalMargin, startHorizontalMargin, startVerticalMargin, endHorizontalMargin, endVerticalMargin, otherHorizontalMargin, otherVerticalMargin, otherBoxes, connStartPoint, connEndPoint, pathStartPoint, pathEndPoint, gridStartPoint, gridEndPoint, startGridBox, endGridBox, grid, path, tryNum;
    noPathFallbackFn = ArrayHelper.asArray(noPathFallbackFn);
    for (tryNum = 0; lineDef && !path; ) {
      lineDefFull = Object.assign(me.config, lineDef);
      startBox = lineDefFull.startBox;
      endBox = lineDefFull.endBox;
      startShift = lineDefFull.startShift;
      endShift = lineDefFull.endShift;
      startSide = lineDefFull.startSide;
      endSide = lineDefFull.endSide;
      startArrowMargin = lineDefFull.startArrowMargin;
      endArrowMargin = lineDefFull.endArrowMargin;
      horizontalMargin = lineDefFull.horizontalMargin;
      verticalMargin = lineDefFull.verticalMargin;
      startHorizontalMargin = lineDefFull.hasOwnProperty("startHorizontalMargin") ? lineDefFull.startHorizontalMargin : horizontalMargin;
      startVerticalMargin = lineDefFull.hasOwnProperty("startVerticalMargin") ? lineDefFull.startVerticalMargin : verticalMargin;
      endHorizontalMargin = lineDefFull.hasOwnProperty("endHorizontalMargin") ? lineDefFull.endHorizontalMargin : horizontalMargin;
      endVerticalMargin = lineDefFull.hasOwnProperty("endVerticalMargin") ? lineDefFull.endVerticalMargin : verticalMargin;
      otherHorizontalMargin = lineDefFull.hasOwnProperty("otherHorizontalMargin") ? lineDefFull.otherHorizontalMargin : horizontalMargin;
      otherVerticalMargin = lineDefFull.hasOwnProperty("otherVerticalMargin") ? lineDefFull.otherVerticalMargin : verticalMargin;
      otherBoxes = lineDefFull.otherBoxes;
      startSide = me.normalizeSide(startSide);
      endSide = me.normalizeSide(endSide);
      connStartPoint = me.getConnectionCoordinatesFromBoxSideShift(startBox, startSide, startShift);
      connEndPoint = me.getConnectionCoordinatesFromBoxSideShift(endBox, endSide, endShift);
      startGridBox = me.calcGridBaseBoxFromBoxAndDrawParams(startBox, startSide, startArrowMargin, startHorizontalMargin, startVerticalMargin);
      endGridBox = me.calcGridBaseBoxFromBoxAndDrawParams(endBox, endSide, endArrowMargin, endHorizontalMargin, endVerticalMargin);
      BOX_PROPERTIES.forEach((property) => {
        if (Math.abs(startGridBox[property] - endGridBox[property]) <= THRESHOLD) {
          endGridBox[property] = startGridBox[property];
        }
      });
      if (me.shouldLookForPath(startBox, endBox, startGridBox, endGridBox)) {
        otherBoxes = otherBoxes == null ? void 0 : otherBoxes.map(
          (box) => me.calcGridBaseBoxFromBoxAndDrawParams(box, false, 0, otherHorizontalMargin, otherVerticalMargin)
        );
        pathStartPoint = me.getConnectionCoordinatesFromBoxSideShift(startGridBox, startSide, startShift);
        pathEndPoint = me.getConnectionCoordinatesFromBoxSideShift(endGridBox, endSide, endShift);
        grid = me.buildPathGrid(startGridBox, endGridBox, pathStartPoint, pathEndPoint, startSide, endSide, otherBoxes);
        gridStartPoint = me.convertDecartPointToGridPoint(grid, pathStartPoint);
        gridEndPoint = me.convertDecartPointToGridPoint(grid, pathEndPoint);
        path = me.findPathOnGrid(grid, gridStartPoint, gridEndPoint, startSide, endSide);
      }
      for (lineDef = false; !path && !lineDef && noPathFallbackFn && tryNum < noPathFallbackFn.length; tryNum++) {
        lineDef = noPathFallbackFn[tryNum](lineDefFull, originalLineDef);
      }
    }
    if (path) {
      path = me.prependPathWithArrowStaffSegment(path, connStartPoint, startSide);
      path = me.appendPathWithArrowStaffSegment(path, connEndPoint, endSide);
      path = me.optimizePath(path);
    }
    return path;
  }
  // Compares boxes relative position in the given direction.
  //  0 - 1 is to the left/top of 2
  //  1 - 1 overlaps with left/top edge of 2
  //  2 - 1 is inside 2
  // -2 - 2 is inside 1
  //  3 - 1 overlaps with right/bottom edge of 2
  //  4 - 1 is to the right/bottom of 2
  static calculateRelativePosition(box1, box2, vertical = false) {
    const startProp = vertical ? "top" : "start", endProp = vertical ? "bottom" : "end";
    let result;
    if (box1[endProp] < box2[startProp]) {
      result = 0;
    } else if (box1[endProp] <= box2[endProp] && box1[endProp] >= box2[startProp] && box1[startProp] < box2[startProp]) {
      result = 1;
    } else if (box1[startProp] >= box2[startProp] && box1[endProp] <= box2[endProp]) {
      result = 2;
    } else if (box1[startProp] < box2[startProp] && box1[endProp] > box2[endProp]) {
      result = -2;
    } else if (box1[startProp] <= box2[endProp] && box1[endProp] > box2[endProp]) {
      result = 3;
    } else {
      result = 4;
    }
    return result;
  }
  // Checks if relative position of the original and marginized boxes is the same
  static boxOverlapChanged(startBox, endBox, gridStartBox, gridEndBox, vertical = false) {
    const calculateOverlap = _RectangularPathFinder.calculateRelativePosition, originalOverlap = calculateOverlap(startBox, endBox, vertical), finalOverlap = calculateOverlap(gridStartBox, gridEndBox, vertical);
    return originalOverlap !== finalOverlap;
  }
  shouldLookForPath(startBox, endBox, gridStartBox, gridEndBox) {
    let result = true;
    if (
      // We refer to the original arrow margins because during lookup those might be nullified and we need some
      // criteria to tell if events are too narrow
      (startBox.end - startBox.start <= this.startArrowMargin || endBox.end - endBox.start <= this.endArrowMargin) && Math.abs(_RectangularPathFinder.calculateRelativePosition(startBox, endBox, true)) === 2
    ) {
      result = !_RectangularPathFinder.boxOverlapChanged(startBox, endBox, gridStartBox, gridEndBox);
    }
    return result;
  }
  getConnectionCoordinatesFromBoxSideShift(box, side, shift) {
    let coords;
    switch (side) {
      case "left":
        coords = {
          x: box.start,
          y: (box.top + box.bottom) / 2 + shift
        };
        break;
      case "right":
        coords = {
          x: box.end,
          y: (box.top + box.bottom) / 2 + shift
        };
        break;
      case "top":
        coords = {
          x: (box.start + box.end) / 2 + shift,
          y: box.top
        };
        break;
      case "bottom":
        coords = {
          x: (box.start + box.end) / 2 + shift,
          y: box.bottom
        };
        break;
    }
    return coords;
  }
  calcGridBaseBoxFromBoxAndDrawParams(box, side, arrowMargin, horizontalMargin, verticalMargin) {
    let gridBox;
    switch (this.normalizeSide(side)) {
      case "left":
        gridBox = {
          start: box.start - Math.max(
            /*arrowSize + */
            arrowMargin,
            horizontalMargin
          ),
          end: box.end + horizontalMargin,
          top: box.top - verticalMargin,
          bottom: box.bottom + verticalMargin
        };
        break;
      case "right":
        gridBox = {
          start: box.start - horizontalMargin,
          end: box.end + Math.max(
            /*arrowSize + */
            arrowMargin,
            horizontalMargin
          ),
          top: box.top - verticalMargin,
          bottom: box.bottom + verticalMargin
        };
        break;
      case "top":
        gridBox = {
          start: box.start - horizontalMargin,
          end: box.end + horizontalMargin,
          top: box.top - Math.max(
            /*arrowSize + */
            arrowMargin,
            verticalMargin
          ),
          bottom: box.bottom + verticalMargin
        };
        break;
      case "bottom":
        gridBox = {
          start: box.start - horizontalMargin,
          end: box.end + horizontalMargin,
          top: box.top - verticalMargin,
          bottom: box.bottom + Math.max(
            /*arrowSize + */
            arrowMargin,
            verticalMargin
          )
        };
        break;
      default:
        gridBox = {
          start: box.start - horizontalMargin,
          end: box.end + horizontalMargin,
          top: box.top - verticalMargin,
          bottom: box.bottom + verticalMargin
        };
    }
    return gridBox;
  }
  normalizeSide(side) {
    const { rtl } = this.client;
    (side2) => sideToSide[side2] || side2;
    if (side === "start") {
      return rtl ? "right" : "left";
    }
    if (side === "end") {
      return rtl ? "left" : "right";
    }
    return side;
  }
  buildPathGrid(startGridBox, endGridBox, pathStartPoint, pathEndPoint, startSide, endSide, otherGridBoxes) {
    let xs, ys, y, x, ix, iy, xslen, yslen, ib, blen, box, permitted, point;
    const points = {}, linearPoints = [];
    xs = [
      startGridBox.start,
      startSide === "left" || startSide === "right" ? (startGridBox.start + startGridBox.end) / 2 : pathStartPoint.x,
      startGridBox.end,
      endGridBox.start,
      endSide === "left" || endSide === "right" ? (endGridBox.start + endGridBox.end) / 2 : pathEndPoint.x,
      endGridBox.end
    ];
    ys = [
      startGridBox.top,
      startSide === "top" || startSide === "bottom" ? (startGridBox.top + startGridBox.bottom) / 2 : pathStartPoint.y,
      startGridBox.bottom,
      endGridBox.top,
      endSide === "top" || endSide === "bottom" ? (endGridBox.top + endGridBox.bottom) / 2 : pathEndPoint.y,
      endGridBox.bottom
    ];
    if (otherGridBoxes) {
      otherGridBoxes.forEach((box2) => {
        xs.push(box2.start, (box2.start + box2.end) / 2, box2.end);
        ys.push(box2.top, (box2.top + box2.bottom) / 2, box2.bottom);
      });
    }
    xs = [...new Set(xs.sort((a, b) => a - b))];
    ys = [...new Set(ys.sort((a, b) => a - b))];
    for (iy = 0, yslen = ys.length; iy < yslen; ++iy) {
      points[iy] = points[iy] || {};
      y = ys[iy];
      for (ix = 0, xslen = xs.length; ix < xslen; ++ix) {
        x = xs[ix];
        permitted = (x <= startGridBox.start || x >= startGridBox.end || y <= startGridBox.top || y >= startGridBox.bottom) && (x <= endGridBox.start || x >= endGridBox.end || y <= endGridBox.top || y >= endGridBox.bottom);
        if (otherGridBoxes) {
          for (ib = 0, blen = otherGridBoxes.length; permitted && ib < blen; ++ib) {
            box = otherGridBoxes[ib];
            permitted = x <= box.start || x >= box.end || y <= box.top || y >= box.bottom || // Allow point if it is a path start/end even if point is inside any box
            x === pathStartPoint.x && y === pathStartPoint.y || x === pathEndPoint.x && y === pathEndPoint.y;
          }
        }
        point = {
          distance: Number.MAX_SAFE_INTEGER,
          permitted,
          x,
          y,
          ix,
          iy
        };
        points[iy][ix] = point;
        linearPoints.push(point);
      }
    }
    return {
      width: xs.length,
      height: ys.length,
      xs,
      ys,
      points,
      linearPoints
    };
  }
  convertDecartPointToGridPoint(grid, point) {
    const x = grid.xs.indexOf(point.x), y = grid.ys.indexOf(point.y);
    return grid.points[y][x];
  }
  findPathOnGrid(grid, gridStartPoint, gridEndPoint, startSide, endSide) {
    const me = this;
    let path = false;
    if (gridStartPoint.permitted && gridEndPoint.permitted) {
      grid = me.waveForward(grid, gridStartPoint, 0);
      path = me.collectPath(grid, gridEndPoint, endSide);
    }
    return path;
  }
  // Returns neighbors from Von Neiman ambit (see Lee pathfinding algorithm description)
  getGridPointNeighbors(grid, gridPoint, predicateFn) {
    const ix = gridPoint.ix, iy = gridPoint.iy, result = [];
    let neighbor;
    if (iy < grid.height - 1) {
      neighbor = grid.points[iy + 1][ix];
      (!predicateFn || predicateFn(neighbor)) && result.push(neighbor);
    }
    if (iy > 0) {
      neighbor = grid.points[iy - 1][ix];
      (!predicateFn || predicateFn(neighbor)) && result.push(neighbor);
    }
    if (ix < grid.width - 1) {
      neighbor = grid.points[iy][ix + 1];
      (!predicateFn || predicateFn(neighbor)) && result.push(neighbor);
    }
    if (ix > 0) {
      neighbor = grid.points[iy][ix - 1];
      (!predicateFn || predicateFn(neighbor)) && result.push(neighbor);
    }
    return result;
  }
  waveForward(grid, gridStartPoint, distance) {
    const me = this;
    WalkHelper.preWalkUnordered(
      // Walk starting point - a node is a grid point and it's distance from the starting point
      [gridStartPoint, distance],
      // Children query function
      // NOTE: It's important to fix neighbor distance first, before waving to a neighbor, otherwise waving might
      //       get through a neighbor point setting it's distance to a value more than (distance + 1) whereas we,
      //       at the children querying moment in time, already know that the possibly optimal distance is (distance + 1)
      ([point, distance2]) => me.getGridPointNeighbors(
        grid,
        point,
        (neighborPoint) => neighborPoint.permitted && neighborPoint.distance > distance2 + 1
      ).map(
        (neighborPoint) => [neighborPoint, distance2 + 1]
        // Neighbor distance fixation
      ),
      // Walk step iterator function
      ([point, distance2]) => point.distance = distance2
      // Neighbor distance applying
    );
    return grid;
  }
  collectPath(grid, gridEndPoint, endSide) {
    const me = this, path = [];
    let pathFound = true, neighbors, lowestDistanceNeighbor, xDiff, yDiff;
    while (pathFound && gridEndPoint.distance) {
      neighbors = me.getGridPointNeighbors(
        grid,
        gridEndPoint,
        (point) => point.permitted && point.distance === gridEndPoint.distance - 1
      );
      pathFound = neighbors.length > 0;
      if (pathFound) {
        neighbors = neighbors.sort((a, b) => {
          let xDiff2, yDiff2;
          xDiff2 = a.ix - gridEndPoint.ix;
          yDiff2 = a.iy - gridEndPoint.iy;
          const resultA = (endSide === "left" || endSide === "right") && yDiff2 === 0 || (endSide === "top" || endSide === "bottom") && xDiff2 === 0 ? -1 : 1;
          xDiff2 = b.ix - gridEndPoint.ix;
          yDiff2 = b.iy - gridEndPoint.iy;
          const resultB = (endSide === "left" || endSide === "right") && yDiff2 === 0 || (endSide === "top" || endSide === "bottom") && xDiff2 === 0 ? -1 : 1;
          if (resultA > resultB)
            return 1;
          if (resultA < resultB)
            return -1;
          if (resultA === resultB)
            return a.y > b.y ? -1 : a.y < b.y ? 1 : 0;
        });
        lowestDistanceNeighbor = neighbors[0];
        path.push({
          x1: lowestDistanceNeighbor.x,
          y1: lowestDistanceNeighbor.y,
          x2: gridEndPoint.x,
          y2: gridEndPoint.y
        });
        xDiff = lowestDistanceNeighbor.ix - gridEndPoint.ix;
        yDiff = lowestDistanceNeighbor.iy - gridEndPoint.iy;
        switch (true) {
          case (!yDiff && xDiff > 0):
            endSide = "left";
            break;
          case (!yDiff && xDiff < 0):
            endSide = "right";
            break;
          case (!xDiff && yDiff > 0):
            endSide = "top";
            break;
          case (!xDiff && yDiff < 0):
            endSide = "bottom";
            break;
        }
        gridEndPoint = lowestDistanceNeighbor;
      }
    }
    return pathFound && path.reverse() || false;
  }
  prependPathWithArrowStaffSegment(path, connStartPoint, startSide) {
    if (path.length > 0) {
      const firstSegment = path[0], prependSegment = {
        x2: firstSegment.x1,
        y2: firstSegment.y1
      };
      switch (startSide) {
        case "left":
          prependSegment.x1 = connStartPoint.x;
          prependSegment.y1 = firstSegment.y1;
          break;
        case "right":
          prependSegment.x1 = connStartPoint.x;
          prependSegment.y1 = firstSegment.y1;
          break;
        case "top":
          prependSegment.x1 = firstSegment.x1;
          prependSegment.y1 = connStartPoint.y;
          break;
        case "bottom":
          prependSegment.x1 = firstSegment.x1;
          prependSegment.y1 = connStartPoint.y;
          break;
      }
      path.unshift(prependSegment);
    }
    return path;
  }
  appendPathWithArrowStaffSegment(path, connEndPoint, endSide) {
    if (path.length > 0) {
      const lastSegment = path[path.length - 1], appendSegment = {
        x1: lastSegment.x2,
        y1: lastSegment.y2
      };
      switch (endSide) {
        case "left":
          appendSegment.x2 = connEndPoint.x;
          appendSegment.y2 = lastSegment.y2;
          break;
        case "right":
          appendSegment.x2 = connEndPoint.x;
          appendSegment.y2 = lastSegment.y2;
          break;
        case "top":
          appendSegment.x2 = lastSegment.x2;
          appendSegment.y2 = connEndPoint.y;
          break;
        case "bottom":
          appendSegment.x2 = lastSegment.x2;
          appendSegment.y2 = connEndPoint.y;
          break;
      }
      path.push(appendSegment);
    }
    return path;
  }
  optimizePath(path) {
    const optPath = [];
    let prevSegment, curSegment;
    if (path.length > 0) {
      prevSegment = path.shift();
      optPath.push(prevSegment);
      while (path.length > 0) {
        curSegment = path.shift();
        if (equalEnough(prevSegment.x1, curSegment.x1) && equalEnough(prevSegment.y1, curSegment.y1) && equalEnough(prevSegment.x2, curSegment.x2) && equalEnough(prevSegment.y2, curSegment.y2)) {
          prevSegment = curSegment;
        } else if (equalEnough(prevSegment.y1, prevSegment.y2) && equalEnough(curSegment.y1, curSegment.y2)) {
          prevSegment.x2 = curSegment.x2;
        } else if (equalEnough(prevSegment.x1, prevSegment.x2) && equalEnough(curSegment.x1, curSegment.x2)) {
          prevSegment.y2 = curSegment.y2;
        } else {
          optPath.push(curSegment);
          prevSegment = curSegment;
        }
      }
    }
    return optPath;
  }
};
RectangularPathFinder._$name = "RectangularPathFinder";

// ../Scheduler/lib/Scheduler/feature/mixin/DependencyLineGenerator.js
function drawingDirection(pointSet) {
  if (pointSet.x1 === pointSet.x2) {
    return pointSet.y2 > pointSet.y1 ? "d" : "u";
  }
  return pointSet.x2 > pointSet.x1 ? "r" : "l";
}
function segmentLength(pointSet) {
  return pointSet.x1 === pointSet.x2 ? pointSet.y2 - pointSet.y1 : pointSet.x2 - pointSet.x1;
}
function arc(pointSet, nextPointSet, radius) {
  const corner = drawingDirection(pointSet) + drawingDirection(nextPointSet), rx = radius * (corner.includes("l") ? -1 : 1), ry = radius * (corner.includes("u") ? -1 : 1), sweep = corner === "ur" || corner === "lu" || corner === "dl" || corner === "rd" ? 1 : 0;
  return `a${rx},${ry} 0 0 ${sweep} ${rx},${ry}`;
}
function line(pointSet, nextPointSet, location2, radius, prevRadius) {
  let line2 = pointSet.x1 === pointSet.x2 ? "v" : "h", useRadius = radius;
  if (radius) {
    const length = segmentLength(pointSet), nextLength = nextPointSet ? Math.abs(segmentLength(nextPointSet)) : Number.MAX_SAFE_INTEGER, sign = Math.sign(length);
    if (prevRadius == null) {
      prevRadius = radius;
    }
    if (Math.abs(length) < radius * 2 || nextLength < radius * 2) {
      useRadius = Math.min(Math.abs(length), nextLength) / 2;
    }
    const subtract = location2 === "single" ? 0 : location2 === "first" ? useRadius : location2 === "between" ? prevRadius + useRadius : (
      /*last*/
      prevRadius
    ), useLength = length - subtract * sign;
    line2 += Math.sign(useLength) !== sign ? 0 : useLength;
    if (location2 !== "last" && location2 !== "single" && useRadius > 0) {
      line2 += ` ${arc(pointSet, nextPointSet, useRadius)}`;
    }
  } else {
    line2 += segmentLength(pointSet);
  }
  return {
    line: line2,
    currentRadius: radius !== useRadius ? useRadius : null
  };
}
function pathMapper(radius, points) {
  const { length } = points;
  if (!length) {
    return "";
  }
  let currentRadius = null;
  return `M${points[0].x1},${points[0].y1} ${points.map((pointSet, i) => {
    const location2 = length === 1 ? "single" : i === length - 1 ? "last" : i === 0 ? "first" : "between", lineSpec = line(pointSet, points[i + 1], location2, radius, currentRadius);
    ({ currentRadius } = lineSpec);
    return lineSpec.line;
  }).join(" ")}`;
}
var DependencyLineGenerator_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    constructor() {
      super(...arguments);
      __publicField(this, "lineCache", {});
    }
    onSVGReady() {
      const me = this;
      me.pathFinder = new RectangularPathFinder({
        ...me.pathFinderConfig,
        client: me.client
      });
      me.lineDefAdjusters = me.createLineDefAdjusters();
      me.createMarker();
    }
    changeRadius(radius) {
      if (radius !== null) {
        ObjectHelper.assertNumber(radius, "radius", true);
      }
      return radius;
    }
    resetAtRuntime() {
      if (!this.isConfiguring) {
        this.reset();
      }
    }
    updateRadius() {
      this.resetAtRuntime();
    }
    updateRenderer() {
      this.resetAtRuntime();
    }
    changeClickWidth(width) {
      if (width !== null) {
        ObjectHelper.assertNumber(width, "clickWidth", true);
      }
      return width;
    }
    updateClickWidth() {
      this.resetAtRuntime();
    }
    updateDrawAroundParents() {
      this.resetAtRuntime();
    }
    //region Marker
    createMarker() {
      var _a6, _b2;
      const me = this, { markerDef } = me, svg = this.client.svgCanvas, markerId = markerDef ? `${me.client.id}-arrowEnd` : "arrowEnd";
      (_a6 = me.marker) == null ? void 0 : _a6.remove();
      svg.style.setProperty("--scheduler-dependency-marker", `url(#${markerId})`);
      me.marker = DomHelper.createElement({
        parent: svg,
        id: markerId,
        tag: "marker",
        className: "b-sch-dependency-arrow",
        ns: "http://www.w3.org/2000/svg",
        markerHeight: 11,
        markerWidth: 11,
        refX: 8.5,
        refY: 3,
        viewBox: "0 0 9 6",
        orient: "auto-start-reverse",
        markerUnits: "userSpaceOnUse",
        retainElement: true,
        children: [{
          tag: "path",
          ns: "http://www.w3.org/2000/svg",
          d: (_b2 = me.markerDef) != null ? _b2 : "M3,0 L3,6 L9,3 z"
        }]
      });
    }
    updateMarkerDef() {
      if (!this.isConfiguring) {
        this.createMarker();
        if (BrowserHelper.isSafari) {
          this.client.svgCanvas.innerHTML += "";
        }
      }
    }
    //endregion
    //region DomConfig
    getAssignmentElement(assignment) {
      var _a6, _b2;
      const proxyElement = (_b2 = (_a6 = this.client.features.eventDrag) == null ? void 0 : _a6.getProxyElement) == null ? void 0 : _b2.call(_a6, assignment);
      return proxyElement || this.client.getElementFromAssignmentRecord(assignment);
    }
    // Generate a DomConfig for a dependency line between two assignments (tasks in Gantt)
    getDomConfigs(dependency, fromAssignment, toAssignment, forceBoxes) {
      var _a6, _b2;
      const me = this, key = me.getDependencyKey(dependency, fromAssignment, toAssignment), cached = me.lineCache[key];
      if (me.constructLineCache || !cached || forceBoxes || me.drawingLive && (me.getAssignmentElement(fromAssignment) || me.getAssignmentElement(toAssignment))) {
        const lineDef = me.prepareLineDef(dependency, fromAssignment, toAssignment, forceBoxes), points = lineDef && me.pathFinder.findPath(lineDef, me.lineDefAdjusters), {
          client,
          clickWidth
        } = me, { toEvent } = dependency;
        if (points) {
          const highlighted = me.highlighted.get(dependency), domConfig = {
            tag: "path",
            ns: "http://www.w3.org/2000/svg",
            d: pathMapper((_a6 = me.radius) != null ? _a6 : 0, points),
            // Prevent adding `role="presentation"` to the path, it should not be needed, the entire SVG is
            // treated as an img by the a11 tree
            role: null,
            dataset: {
              syncId: key,
              depId: dependency.id,
              fromId: fromAssignment.id,
              toId: toAssignment.id
            },
            elementData: {
              dependency,
              points
            },
            class: {
              [me.baseCls]: 1,
              [dependency.cls]: dependency.cls,
              // Data highlight
              [dependency.highlighted]: dependency.highlighted,
              // Feature highlight
              [highlighted && [...highlighted].join(" ")]: highlighted,
              [me.noMarkerCls]: lineDef.hideMarker,
              "b-inactive": dependency.active === false,
              "b-sch-bidirectional-line": dependency.bidirectional,
              "b-readonly": dependency.readOnly,
              // If target event is outside the view add special CSS class to hide marker (arrow)
              "b-sch-dependency-ends-outside": !toEvent.milestone && (client.showUnscheduledTasks && !toEvent.isScheduled ? toEvent._endDate <= client.startDate || client.endDate <= toEvent._startDate : toEvent.endDate <= client.startDate || client.endDate <= toEvent.startDate) || toEvent.milestone && (toEvent.endDate < client.startDate || client.endDate < toEvent.startDate)
            }
          };
          (_b2 = me.renderer) == null ? void 0 : _b2.call(me, {
            domConfig,
            points,
            dependencyRecord: dependency,
            fromAssignmentRecord: fromAssignment,
            toAssignmentRecord: toAssignment,
            fromBox: lineDef.startBox,
            toBox: lineDef.endBox,
            fromSide: lineDef.startSide,
            toSide: lineDef.endSide
          });
          const configs = [domConfig];
          if (clickWidth > 1) {
            configs.push({
              ...domConfig,
              // Shallow on purpose, to not waste perf cloning deeply
              class: {
                ...domConfig.class,
                "b-click-area": 1
              },
              dataset: {
                ...domConfig.dataset,
                syncId: `${domConfig.dataset.syncId}-click-area`
              },
              style: {
                strokeWidth: clickWidth
              }
            });
          }
          return me.lineCache[key] = configs;
        }
        return me.lineCache[key] = null;
      }
      return cached;
    }
    //endregion
    //region Bounds
    // Generates `otherBoxes` config for rectangular path finder, which push dependency line to the row boundary.
    // It should be enough to return single box with top/bottom taken from row top/bottom and left/right taken from source
    // box, extended by start arrow margin to both sides.
    generateBoundaryBoxes(box, side) {
      if (side === "top") {
        return [];
      }
      if (side === "bottom") {
        return [
          {
            start: box.left,
            end: box.left + box.width / 2,
            top: box.rowTop,
            bottom: box.rowBottom
          },
          {
            start: box.left + box.width / 2,
            end: box.right,
            top: box.rowTop,
            bottom: box.rowBottom
          }
        ];
      } else {
        return [
          {
            start: box.left - this.pathFinder.startArrowMargin,
            end: box.right + this.pathFinder.startArrowMargin,
            top: box.rowTop,
            bottom: box.rowBottom
          }
        ];
      }
    }
    // Bounding box for an assignment, uses elements bounds if rendered
    getAssignmentBounds(assignment) {
      const { client } = this, element = this.getAssignmentElement(assignment);
      if (element && !client.isExporting) {
        const rectangle = Rectangle.from(element, this.relativeTo);
        rectangle.fromElement = true;
        if (client.isHorizontal) {
          let row = client.getRowById(assignment.resource.id);
          if (row) {
            if (rectangle.y < row.top || rectangle.bottom > row.bottom) {
              const overRow = client.rowManager.getRowAt(rectangle.center.y, true);
              if (overRow) {
                row = overRow;
              }
            }
            rectangle.rowTop = row.top;
            rectangle.rowBottom = row.bottom;
          } else {
            return client.getAssignmentEventBox(assignment, true);
          }
        }
        return rectangle;
      }
      return client.isEngineReady && client.getAssignmentEventBox(assignment, true);
    }
    //endregion
    //region Sides
    getConnectorStartSide(timeSpanRecord) {
      return this.client.currentOrientation.getConnectorStartSide(timeSpanRecord);
    }
    getConnectorEndSide(timeSpanRecord) {
      return this.client.currentOrientation.getConnectorEndSide(timeSpanRecord);
    }
    getDependencyStartSide(dependency) {
      const { fromEvent, type, fromSide } = dependency;
      if (fromSide) {
        return fromSide;
      }
      switch (true) {
        case type === DependencyModel.Type.StartToEnd:
        case type === DependencyModel.Type.StartToStart:
          return this.getConnectorStartSide(fromEvent);
        case type === DependencyModel.Type.EndToStart:
        case type === DependencyModel.Type.EndToEnd:
          return this.getConnectorEndSide(fromEvent);
        default:
          return this.getConnectorEndSide(fromEvent);
      }
    }
    getDependencyEndSide(dependency) {
      const { toEvent, type, toSide } = dependency;
      if (toSide) {
        return toSide;
      }
      switch (true) {
        case type === DependencyModel.Type.EndToEnd:
        case type === DependencyModel.Type.StartToEnd:
          return this.getConnectorEndSide(toEvent);
        case type === DependencyModel.Type.EndToStart:
        case type === DependencyModel.Type.StartToStart:
          return this.getConnectorStartSide(toEvent);
        default:
          return this.getConnectorStartSide(toEvent);
      }
    }
    //endregion
    //region Line def
    // An array of functions used to alter path config when no path found.
    // It first tries to shrink arrow margins and secondly hides arrows entirely
    createLineDefAdjusters() {
      var _a6;
      const { client } = this;
      function shrinkArrowMargins(lineDef) {
        const { barMargin } = client;
        if (lineDef.startArrowMargin > barMargin || lineDef.endArrowMargin > barMargin) {
          lineDef.startArrowMargin = lineDef.endArrowMargin = barMargin;
          return lineDef;
        }
        return false;
      }
      function resetArrowMargins(lineDef) {
        if (lineDef.startArrowMargin > 0 || lineDef.endArrowMargin > 0) {
          lineDef.startArrowMargin = lineDef.endArrowMargin = 0;
          return lineDef;
        }
        return false;
      }
      function shrinkHorizontalMargin(lineDef, originalLineDef) {
        if (lineDef.horizontalMargin > 2) {
          lineDef.horizontalMargin = 1;
          originalLineDef.hideMarker = true;
          return lineDef;
        }
        return false;
      }
      const adjusters = [
        shrinkArrowMargins,
        resetArrowMargins,
        shrinkHorizontalMargin
      ];
      if ((_a6 = client.features.nestedEvents) == null ? void 0 : _a6.enabled) {
        adjusters.unshift((lineDef) => {
          if (lineDef.otherBoxes.length) {
            lineDef.otherBoxes.length = lineDef.otherBoxes.nestedStart;
          }
          return lineDef;
        });
      }
      return adjusters;
    }
    // Overridden in Gantt
    adjustLineDef(dependency, lineDef) {
      return lineDef;
    }
    // Prepare data to feed to the path finder
    prepareLineDef(dependency, fromAssignment, toAssignment, forceBoxes) {
      var _a6, _b2, _c2, _d;
      const me = this, { client } = me, startSide = me.getDependencyStartSide(dependency), endSide = me.getDependencyEndSide(dependency), startRectangle = (_a6 = forceBoxes == null ? void 0 : forceBoxes.from) != null ? _a6 : me.getAssignmentBounds(fromAssignment), endRectangle = (_b2 = forceBoxes == null ? void 0 : forceBoxes.to) != null ? _b2 : me.getAssignmentBounds(toAssignment), otherBoxes = [];
      if (!startRectangle || !endRectangle) {
        return null;
      }
      let {
        startArrowMargin,
        verticalMargin
      } = me.pathFinder;
      if (client.isHorizontal) {
        if (client.rtl) {
          const { totalSize } = client.timeAxisViewModel;
          if (startRectangle && !startRectangle.fromElement) {
            startRectangle._x = totalSize - startRectangle.x - startRectangle.width;
          }
          if (endRectangle && !endRectangle.fromElement) {
            endRectangle._x = totalSize - endRectangle.x - endRectangle.width;
          }
        }
        if (startRectangle.rowTop != null && startRectangle.rowTop !== endRectangle.rowTop) {
          otherBoxes.push(...me.generateBoundaryBoxes(startRectangle, startSide));
        }
        if (((_c2 = client.features.nestedEvents) == null ? void 0 : _c2.enabled) && me.drawAroundParents) {
          const { resourceStore } = client, fromResource = fromAssignment.resource, toResource = toAssignment.resource, fromIndex = resourceStore.indexOf(fromResource), toIndex = resourceStore.indexOf(toResource), minIndex = Math.min(fromIndex, toIndex), maxIndex = Math.max(fromIndex, toIndex);
          otherBoxes.nestedStart = otherBoxes.length;
          for (const assignment of client.assignmentStore) {
            if (assignment !== fromAssignment && assignment !== toAssignment && ((_d = assignment.event) == null ? void 0 : _d.isParent) && fromAssignment.event.parent !== assignment.event && toAssignment.event.parent !== assignment.event) {
              const currentIndex = resourceStore.indexOf(assignment.resource);
              if (currentIndex >= minIndex && currentIndex <= maxIndex) {
                const assignmentBox = me.getAssignmentBounds(assignment).inflate(startArrowMargin);
                assignmentBox.isNestedParent = true;
                otherBoxes.push(assignmentBox);
              }
            }
          }
        }
        if (!dependency.bidirectional) {
          if (/(top|bottom)/.test(startSide)) {
            startArrowMargin = client.barMargin / 2;
          }
          verticalMargin = client.barMargin / 2;
        }
      }
      return me.adjustLineDef(dependency, {
        startBox: startRectangle,
        endBox: endRectangle,
        otherBoxes,
        startArrowMargin,
        verticalMargin,
        otherVerticalMargin: 0,
        otherHorizontalMargin: 0,
        startSide,
        endSide
      });
    }
    //endregion
    //region Cache
    // All dependencies are about to be drawn, check if we need to build the line cache
    beforeDraw() {
      super.beforeDraw();
      if (!Object.keys(this.lineCache).length) {
        this.constructLineCache = true;
      }
    }
    // All dependencies are drawn, we no longer need to rebuild the cache
    afterDraw() {
      super.afterDraw();
      this.constructLineCache = false;
    }
    reset() {
      super.reset();
      this.lineCache = {};
    }
    //endregion
  }, __publicField(_a5, "$name", "DependencyLineGenerator"), _a5;
};

// ../Scheduler/lib/Scheduler/feature/mixin/DependencyTooltip.js
var fromBoxSide = [
  "start",
  "start",
  "end",
  "end"
];
var toBoxSide = [
  "start",
  "end",
  "start",
  "end"
];
var DependencyTooltip_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    changeTooltip(tooltip, old) {
      const me = this;
      old == null ? void 0 : old.destroy();
      if (!tooltip) {
        return null;
      }
      const instance = Tooltip.new({
        align: "b-t",
        id: `${me.client.id}-dependency-tip`,
        forSelector: `.b-timelinebase:not(.b-eventeditor-editing,.b-taskeditor-editing,.b-resizing-event,.b-dragcreating,.b-dragging-event,.b-creating-dependency) .${me.baseCls}`,
        forElement: me.client.timeAxisSubGridElement,
        showOnHover: true,
        hoverDelay: 0,
        hideDelay: 0,
        anchorToTarget: false,
        textContent: false,
        // Skip max-width setting
        trackMouse: false,
        getHtml: me.getHoverTipHtml.bind(me)
      }, tooltip);
      instance.element.classList.add("b-sch-dependency-tooltip");
      return instance;
    }
    updateShowTooltip(value2) {
      const me = this;
      if (me.isConfiguring && !value2) {
        return;
      }
      if (value2 && !me._tooltip) {
        me.tooltip;
      }
      if (me._tooltip) {
        me._tooltip.disabled = !value2;
      }
    }
    /**
     * Generates DomConfig content for the tooltip shown when hovering a dependency
     * @param {Object} tooltipConfig
     * @returns {DomConfig} DomConfig used as tooltips content
     * @private
     */
    getHoverTipHtml({ activeTarget }) {
      return this.tooltipTemplate(this.resolveDependencyRecord(activeTarget));
    }
  }, __publicField(_a5, "$name", "DependencyTooltip"), __publicField(_a5, "configurable", {
    /**
     * Set to `true` to show a tooltip when hovering a dependency line
     * @prp {Boolean}
     * @default
     * @category Dependency tooltip
     */
    showTooltip: true,
    /**
     * Set to `true` to show the lag in the tooltip
     * @config {Boolean}
     * @default
     */
    showLagInTooltip: false,
    /**
     * A template function allowing you to configure the contents of the tooltip shown when hovering a
     * dependency line. You can return either an HTML string or a {@link DomConfig} object.
     * @prp {Function} tooltipTemplate
     * @param {Scheduler.model.DependencyBaseModel} dependency The dependency record
     * @returns {String|DomConfig}
     * @category Dependency tooltip
     */
    tooltipTemplate(dependency) {
      const me = this;
      return [
        { tag: "label", text: me.L("L{Dependencies.from}") },
        { text: dependency.fromEvent.name },
        { className: `b-sch-box b-${dependency.fromSide || fromBoxSide[dependency.type]}` },
        { tag: "label", text: me.L("L{Dependencies.to}") },
        { text: dependency.toEvent.name },
        { className: `b-sch-box b-${dependency.toSide || toBoxSide[dependency.type]}` },
        me.showLagInTooltip && { tag: "label", text: me.L("L{DependencyEdit.Lag}") },
        me.showLagInTooltip && { text: `${dependency.lag || 0} ${DateHelper.getLocalizedNameOfUnit(dependency.lagUnit, dependency.lag !== 1)}` }
      ];
    },
    /**
     * A tooltip config object that will be applied to the dependency hover tooltip. Can be used to for example
     * customize delay
     * @config {TooltipConfig}
     * @category Dependency tooltip
     */
    tooltip: {
      $config: ["nullify", "lazy"],
      value: {}
    }
  }), _a5;
};

// ../Scheduler/lib/Scheduler/feature/Dependencies.js
var eventNameMap2 = {
  click: "Click",
  dblclick: "DblClick",
  contextmenu: "ContextMenu"
};
var collectLinkedAssignments = (assignment) => {
  var _a5;
  const result = [assignment];
  if ((_a5 = assignment.resource) == null ? void 0 : _a5.hasLinks) {
    result.push(...assignment.resource.$links.map((l) => ({
      id: `${l.id}_${assignment.id}`,
      resource: l,
      event: assignment.event,
      drawDependencies: assignment.drawDependencies
    })));
  }
  return result;
};
var Dependencies = class extends InstancePlugin.mixin(
  AttachToProjectMixin_default,
  Delayable_default,
  DependencyCreation_default,
  DependencyGridCache_default,
  DependencyLineGenerator_default,
  DependencyTooltip_default
) {
  constructor() {
    super(...arguments);
    __publicField(this, "domConfigs", /* @__PURE__ */ new Map());
    __publicField(this, "drawingLive", false);
    __publicField(this, "lastScrollX", null);
    __publicField(this, "highlighted", /* @__PURE__ */ new Map());
    // Cached lookups
    __publicField(this, "visibleResources", null);
    __publicField(this, "usingLinks", null);
    __publicField(this, "visibleDateRange", null);
    __publicField(this, "relativeTo", null);
    __publicField(this, "generation", 0);
  }
  static get pluginConfig() {
    return {
      chain: ["render", "onInternalPaint", "onElementClick", "onElementDblClick", "onElementContextMenu", "onElementMouseOver", "onElementMouseOut", "onElementPointerDown", "bindStore"],
      assign: ["getElementForDependency", "getElementsForDependency", "resolveDependencyRecord"]
    };
  }
  //endregion
  //region Init & destroy
  construct(client, config) {
    super.construct(client, config);
    const { scheduledEventName } = client;
    client.ion({
      svgCanvasCreated: "onSVGReady",
      // These events trigger live refresh behaviour
      animationStart: "refreshIfLive",
      // eventDrag in Scheduler, taskDrag in Gantt
      [scheduledEventName + "DragStart"]: "refreshIfLive",
      [scheduledEventName + "DragAbort"]: "refreshIfLive",
      [scheduledEventName + "ResizeStart"]: "refreshIfLive",
      [scheduledEventName + "SegmentDragStart"]: "refreshIfLive",
      [scheduledEventName + "SegmentResizeStart"]: "refreshIfLive",
      // These events shift the surroundings to such extent that grid cache needs rebuilding to be sure that
      // all dependencies are considered
      timelineViewportResize: "immediateReset",
      timeAxisViewModelUpdate: "reset",
      toggleNode: "reset",
      thisObj: this
    });
    client.rowManager.ion({
      refresh: "reset",
      // For example when changing barMargin or rowHeight
      changeTotalHeight: "reset",
      // For example when collapsing groups
      thisObj: this
    });
    this.bindStore(client.store);
  }
  doDisable(disable) {
    if (!this.isConfiguring) {
      this._isDisabling = disable;
      this.draw();
      this._isDisabling = false;
    }
    super.doDisable(disable);
  }
  //endregion
  //region RefreshTriggers
  get rowStore() {
    return this.client.isVertical ? this.client.resourceStore : this.client.store;
  }
  // Needed for Gantt, to return Gantts store instead of the project taskStore when using a display store
  get eventStore() {
    return this.client.project.eventStore;
  }
  // React to replacing or refreshing a display store
  bindStore(store) {
    const me = this;
    if (!me.client.isVertical) {
      me.detachListeners("store");
      if (me.client.usesDisplayStore) {
        store == null ? void 0 : store.ion({
          name: "store",
          refresh: "onStoreRefresh",
          thisObj: me
        });
      }
      me.usingLinks = null;
      me.immediateReset();
    }
  }
  onStoreRefresh() {
    this.reset();
  }
  attachToProject(project) {
    super.attachToProject(project);
    project == null ? void 0 : project.ion({
      name: "project",
      commitFinalized: "reset",
      thisObj: this
    });
  }
  attachToResourceStore(resourceStore) {
    super.attachToResourceStore(resourceStore);
    resourceStore == null ? void 0 : resourceStore.ion({
      name: "resourceStore",
      change: "onResourceStoreChange",
      refresh: "onResourceStoreChange",
      thisObj: this
    });
  }
  onResourceStoreChange() {
    this.usingLinks = null;
    this.reset();
  }
  attachToEventStore(eventStore) {
    super.attachToEventStore(eventStore);
    eventStore == null ? void 0 : eventStore.ion({
      name: "eventStore",
      refresh: "reset",
      thisObj: this
    });
  }
  attachToAssignmentStore(assignmentStore) {
    super.attachToAssignmentStore(assignmentStore);
    assignmentStore == null ? void 0 : assignmentStore.ion({
      name: "assignmentStore",
      refresh: "reset",
      thisObj: this
    });
  }
  attachToDependencyStore(dependencyStore) {
    super.attachToDependencyStore(dependencyStore);
    dependencyStore == null ? void 0 : dependencyStore.ion({
      name: "dependencyStore",
      change: "reset",
      refresh: "onDependencyStoreRefresh",
      thisObj: this
    });
  }
  onDependencyStoreRefresh({ action }) {
    if (action === "dataset") {
      this.generation++;
    }
    this.reset();
  }
  updateDrawOnScroll(drawOnScroll) {
    const me = this;
    me.detachListeners("scroll");
    if (drawOnScroll) {
      me.client.ion({
        name: "scroll",
        scroll: "doRefresh",
        horizontalScroll: "onHorizontalScroll",
        prio: -100,
        // After Scheduler draws on scroll, since we target elements
        thisObj: me
      });
    } else {
      me.client.scrollable.ion({
        name: "scroll",
        scrollEnd: "draw",
        thisObj: me
      });
      me.client.timeAxisSubGrid.scrollable.ion({
        name: "scroll",
        scrollEnd: "draw",
        thisObj: me
      });
    }
  }
  onHorizontalScroll({ subGrid, scrollX }) {
    if (scrollX !== this.lastScrollX && subGrid === this.client.timeAxisSubGrid) {
      this.lastScrollX = scrollX;
      this.draw();
    }
  }
  onInternalPaint() {
    this.refresh();
  }
  //endregion
  //region Dependency types
  // Used by DependencyField
  static getLocalizedDependencyType(type) {
    return type ? this.L(`L{DependencyType.${type}}`) : "";
  }
  //endregion
  //region Elements
  getElementForDependency(dependency, fromAssignment, toAssignment) {
    return this.getElementsForDependency(dependency, fromAssignment, toAssignment)[0];
  }
  // NOTE: If we ever make this public we should change it to use the syncIdMap. Currently not needed since only
  // used in tests
  getElementsForDependency(dependency, fromAssignment, toAssignment) {
    let selector = `[data-dep-id="${dependency.id}"]`;
    if (fromAssignment) {
      selector += `[data-from-id="${fromAssignment.id}"]`;
    }
    if (toAssignment) {
      selector += `[data-to-id="${toAssignment.id}"]`;
    }
    return Array.from(this.client.svgCanvas.querySelectorAll(selector));
  }
  /**
   * Returns the dependency record for a DOM element
   * @param {HTMLElement} element The dependency line element
   * @returns {Scheduler.model.DependencyModel} The dependency record
   */
  resolveDependencyRecord(element) {
    var _a5;
    return (_a5 = element.elementData) == null ? void 0 : _a5.dependency;
  }
  isDependencyElement(element) {
    return element.matches(`.${this.baseCls}`);
  }
  //endregion
  //region DOM Events
  onElementPointerDown({ target }) {
    const me = this, { client } = me;
    if (me.enableDelete && !client.readOnly && target !== me.removeIconElement) {
      me.removeRemoveIcon();
      DomHelper.removeClsGlobally(client.svgCanvas, "b-sch-dependency-selected");
    }
  }
  onElementClick(event) {
    const dependency = this.resolveDependencyRecord(event.target);
    if (dependency) {
      const eventName = eventNameMap2[event.type];
      if (event.type === "click") {
        this.onDependencyClick(event.target);
      }
      this.client.trigger(`dependency${eventName}`, {
        dependency,
        event
      });
    }
  }
  onDependencyClick(element) {
    const me = this, { client } = me, { svgCanvas } = client, { dependency } = element.elementData;
    if (me.enableDelete && !client.readOnly && !dependency.readOnly) {
      const point = element.getPointAtLength(client.isGanttBase ? 0 : element.getTotalLength());
      if (!client.topCanvas) {
        client.topCanvas = DomHelper.createElement({
          className: "b-sch-canvas b-sch-top-canvas",
          parent: client.foregroundCanvas.parentElement
        });
      }
      me.removeIconElement = DomHelper.createElement({
        className: "b-sch-dependency-remove-icon " + me.removeIconCls,
        retainElement: true,
        style: {
          insetInlineStart: point.x + "px",
          top: point.y + "px"
        },
        dataset: {
          depId: dependency.id
        },
        elementData: {
          dependencyEl: element
        },
        parent: client.topCanvas
      });
      me.removeIconElement.addEventListener("click", me.onRemoveIconClick.bind(me));
      for (const depEl of me.getElementsForDependency(dependency)) {
        depEl.classList.add("b-sch-dependency-selected");
        if (svgCanvas.moveBefore) {
          svgCanvas.moveBefore(depEl, null);
        } else {
          svgCanvas.appendChild(depEl);
        }
      }
    }
  }
  positionRemoveIcon() {
    var _a5;
    const { dependencyEl } = ((_a5 = this.removeIconElement) == null ? void 0 : _a5.elementData) || {};
    if (dependencyEl) {
      const point = dependencyEl.getPointAtLength(this.client.isGanttBase ? 0 : dependencyEl.getTotalLength());
      Object.assign(this.removeIconElement.style, {
        insetInlineStart: point.x + "px",
        top: point.y + "px"
      });
    }
  }
  // Animated removal of the remove icon
  async removeRemoveIcon() {
    const { removeIconElement } = this;
    if (removeIconElement) {
      await this.client.executeAndAwaitAnimations(
        removeIconElement,
        () => removeIconElement.classList.add("b-removing")
      );
      removeIconElement.remove();
    }
  }
  onRemoveIconClick({ target }) {
    this.client.dependencyStore.remove(target.dataset.depId);
    this.removeRemoveIcon();
  }
  onElementDblClick(event) {
    return this.onElementClick(event);
  }
  onElementContextMenu(event) {
    return this.onElementClick(event);
  }
  onElementMouseOver(event) {
    const me = this, dependency = me.resolveDependencyRecord(event.target);
    if (dependency) {
      me.client.trigger("dependencyMouseOver", {
        dependency,
        event
      });
      if (me.overCls) {
        me.highlight(dependency);
      }
    }
  }
  onElementMouseOut(event) {
    const me = this, dependency = me.resolveDependencyRecord(event.target);
    if (dependency) {
      me.client.trigger("dependencyMouseOut", {
        dependency,
        event
      });
      if (me.overCls) {
        me.unhighlight(dependency);
      }
    }
  }
  //endregion
  //region Export
  // Export calls this fn to determine if a dependency should be included or not
  isDependencyVisible(dependency) {
    const me = this, { rowStore } = me, {
      fromEvent,
      toEvent
    } = dependency;
    if (!fromEvent || !toEvent) {
      return false;
    }
    const fromResource = fromEvent.resource, toResource = toEvent.resource;
    if (!rowStore.isAvailable(fromResource) || !rowStore.isAvailable(toResource)) {
      return false;
    }
    return fromEvent.isModel && !fromResource.instanceMeta(rowStore).hidden && !toResource.instanceMeta(rowStore).hidden;
  }
  //endregion
  //region Highlight
  updateHighlightDependenciesOnEventHover(enable) {
    const me = this;
    if (enable) {
      const { client } = me;
      client.ion({
        name: "highlightOnHover",
        [`${client.scheduledEventName}MouseEnter`]: (params) => me.highlightEventDependencies(params.eventRecord || params.taskRecord),
        [`${client.scheduledEventName}MouseLeave`]: (params) => me.unhighlightEventDependencies(params.eventRecord || params.taskRecord),
        thisObj: me
      });
    } else {
      me.detachListeners("highlightOnHover");
    }
  }
  highlight(dependency, cls = this.overCls) {
    let classes = this.highlighted.get(dependency);
    if (!classes) {
      this.highlighted.set(dependency, classes = /* @__PURE__ */ new Set());
    }
    classes.add(cls);
    for (const element of this.getElementsForDependency(dependency)) {
      element.classList.add(cls);
    }
  }
  unhighlight(dependency, cls = this.overCls) {
    var _a5;
    const classes = (_a5 = this.highlighted) == null ? void 0 : _a5.get(dependency);
    if (classes) {
      classes.delete(cls);
      if (!classes.size) {
        this.highlighted.delete(dependency);
      }
    }
    for (const element of this.getElementsForDependency(dependency)) {
      element.classList.remove(cls);
    }
  }
  highlightEventDependencies(timespan, cls) {
    timespan.dependencies.forEach((dep) => this.highlight(dep, cls));
  }
  unhighlightEventDependencies(timespan, cls) {
    timespan.dependencies.forEach((dep) => this.unhighlight(dep, cls));
  }
  //endregion
  //region Drawing
  // Implemented in DependencyGridCache to return dependencies that might intersect the current viewport and thus
  // should be considered for drawing. Fallback value here is used when there is no grid cache (which happens when it
  // is reset. Also useful in case we want to have it configurable or opt out automatically for small datasets)
  getDependenciesToConsider(startMS, endMS, startIndex, endIndex) {
    var _a5;
    const { eventStore } = this, cachedDeps = (_a5 = super.getDependenciesToConsider) == null ? void 0 : _a5.call(this, startMS, endMS, startIndex, endIndex);
    return (cachedDeps == null ? void 0 : cachedDeps.size) ? cachedDeps : this.project.dependencyStore.records.filter((d) => d.isValid && !eventStore.isFiltered || eventStore.isAvailable(d.fromEvent) && eventStore.isAvailable(d.toEvent));
  }
  // String key used as syncId
  getDependencyKey(dependency, fromAssignment, toAssignment) {
    return `dep:${dependency.id};from:${fromAssignment.id};to:${toAssignment.id};gen:${this.generation}`;
  }
  drawDependency(dependency, batch = false, forceBoxes = null) {
    var _a5, _b2, _c2, _d;
    const me = this, {
      domConfigs,
      client,
      rowStore,
      topIndex,
      bottomIndex
    } = me, {
      useInitialAnimation
    } = client, { idMap } = rowStore, {
      startMS,
      endMS
    } = me.visibleDateRange, {
      fromEvent,
      toEvent
    } = dependency;
    let fromAssigned = fromEvent.assigned, toAssigned = toEvent.assigned;
    if (
      // No point in trying to draw dep between unscheduled/non-existing events
      fromEvent.isScheduled && toEvent.isScheduled && // Or unassigned ones
      (fromAssigned == null ? void 0 : fromAssigned.size) && (toAssigned == null ? void 0 : toAssigned.size)
    ) {
      if (me.usingLinks) {
        fromAssigned = [...fromAssigned].flatMap(collectLinkedAssignments);
        toAssigned = [...toAssigned].flatMap(collectLinkedAssignments);
      }
      for (const from of fromAssigned) {
        for (const to of toAssigned) {
          const fromIndex = (_b2 = idMap[(_a5 = from.resource) == null ? void 0 : _a5.id]) == null ? void 0 : _b2.index, toIndex = (_d = idMap[(_c2 = to.resource) == null ? void 0 : _c2.id]) == null ? void 0 : _d.index, fromDateMS = Math.min(fromEvent.startDateMS, toEvent.startDateMS), toDateMS = Math.max(fromEvent.endDateMS, toEvent.endDateMS);
          if (client.isExporting || fromIndex != null && toIndex != null && (from.drawDependencies !== false && to.drawDependencies !== false) && (rowStore.isAvailable(from.resource) && rowStore.isAvailable(to.resource)) && !// Both ends above view
          (fromIndex < topIndex && toIndex < topIndex || // Both ends below view
          fromIndex > bottomIndex && toIndex > bottomIndex || // Both ends before view
          fromDateMS < startMS && toDateMS < startMS || // Both ends after view
          fromDateMS > endMS && toDateMS > endMS)) {
            const key = me.getDependencyKey(dependency, from, to), lineDomConfigs = me.getDomConfigs(dependency, from, to, forceBoxes);
            if (lineDomConfigs) {
              if (useInitialAnimation) {
                lineDomConfigs[0].style = {
                  animationDelay: `${Math.max(fromIndex, toIndex) / 20 * 1e3}ms`
                };
              }
              domConfigs.set(key, lineDomConfigs);
            } else {
              domConfigs.delete(key);
            }
          }
          me.afterDrawDependency(dependency, fromIndex, toIndex, fromDateMS, toDateMS);
        }
      }
    }
    if (!batch) {
      me.domSync();
    }
  }
  // Hooks used by grid cache, to keep code in this file readable
  afterDrawDependency(dependency, fromIndex, toIndex, fromDateMS, toDateMS) {
    var _a5;
    (_a5 = super.afterDrawDependency) == null ? void 0 : _a5.call(this, dependency, fromIndex, toIndex, fromDateMS, toDateMS);
  }
  beforeDraw() {
    var _a5;
    (_a5 = super.beforeDraw) == null ? void 0 : _a5.call(this);
  }
  afterDraw() {
    var _a5;
    (_a5 = super.afterDraw) == null ? void 0 : _a5.call(this);
  }
  // Update DOM
  domSync(targetElement = this.client.svgCanvas, batch = false) {
    DomSync.sync({
      targetElement,
      domConfig: {
        onlyChildren: true,
        children: Array.from(this.domConfigs.values()).flat()
      },
      syncIdField: "syncId",
      releaseThreshold: 0,
      strict: true,
      callback() {
      }
    });
    if (batch) {
      this.clearDomConfigs();
    }
  }
  fillDrawingCache() {
    const me = this, { client, rowStore } = me;
    me.relativeTo = Rectangle.from(client.svgCanvas);
    me.visibleResources = client.visibleResources;
    me.visibleDateRange = client.visibleDateRange;
    me.topIndex = rowStore.indexOf(me.visibleResources.first);
    me.bottomIndex = rowStore.indexOf(me.visibleResources.last);
    if (me.usingLinks == null) {
      me.usingLinks = rowStore.isChainedTree || client.resourceStore.some((r) => r.hasLinks);
    }
  }
  clearDomConfigs() {
    this.domConfigs.clear();
  }
  // Draw all dependencies intersecting the current viewport immediately
  draw() {
    var _a5;
    const me = this, { client } = me, { visibleDateRange } = client;
    if (client.refreshSuspended || !client.foregroundCanvas || !visibleDateRange || !client.isEngineReady || me.disabled && !me._isDisabling || client.isExporting) {
      return;
    }
    me.fillDrawingCache();
    me.clearDomConfigs();
    if (client.firstVisibleRow && client.lastVisibleRow && client.timeAxis.count && !me.disabled && visibleDateRange.endMS - visibleDateRange.startMS > 0) {
      const {
        topIndex,
        bottomIndex
      } = me, dependencies = me.getDependenciesToConsider(visibleDateRange.startMS, visibleDateRange.endMS, topIndex, bottomIndex);
      me.beforeDraw();
      for (const dependency of dependencies) {
        me.drawDependency(dependency, true);
      }
      me.afterDraw();
      if ((_a5 = me.removeIconElement) == null ? void 0 : _a5.isConnected) {
        client.waitForAnimations().then(() => me.positionRemoveIcon());
      }
    }
    me.domSync();
    client.trigger("dependenciesDrawn");
  }
  //endregion
  //region Refreshing
  // Performs a draw on next frame, not intended to be called directly, call refresh() instead
  async doRefresh() {
    var _a5, _b2, _c2, _d, _e;
    const me = this, { client } = me, { scheduledEventName, features } = client, isAnimating = Boolean(client.isAnimating || client.useInitialAnimation && me.eventStore.count);
    me.draw();
    me.drawingLive = client.dependencyStore.count && (isAnimating || ((_a5 = features[`${scheduledEventName}Drag`]) == null ? void 0 : _a5.isActivelyDragging) || ((_b2 = features[`${scheduledEventName}Drag`]) == null ? void 0 : _b2.isAborting) || ((_c2 = features[`${scheduledEventName}Resize`]) == null ? void 0 : _c2.isResizing) || ((_d = features[`${scheduledEventName}SegmentDrag`]) == null ? void 0 : _d.isActivelyDragging) || ((_e = features[`${scheduledEventName}SegmentResize`]) == null ? void 0 : _e.isResizing));
    if (me.drawOnEventInteraction) {
      me.drawingLive && me.refresh(false, true);
    } else if (isAnimating) {
      if (client.useInitialAnimation) {
        await client.nextAnimationFrame();
      }
      await AsyncHelper.waitForAnimations(client.foregroundCanvas);
      me.draw();
    }
  }
  rafRefresh() {
    this.doRefresh.now();
  }
  refreshIfLive(...args) {
    this.drawOnEventInteraction && this.refresh(...args);
  }
  /**
   * Redraws dependencies on the next animation frame
   */
  refresh(immediateRefresh = this.immediateRefresh, rafRefresh = false) {
    const me = this, { client } = me;
    if (!client.refreshSuspended && !me.disabled && client.isPainted && !client.timeAxisSubGrid.collapsed) {
      if (immediateRefresh) {
        me.doRefresh.now();
      } else if (rafRefresh) {
        me.rafRefresh();
      } else {
        me.doRefresh();
      }
    }
  }
  // Resets grid cache and performs a draw on next frame. Conditions when it should be called:
  // * Zooming
  // * Shifting time axis
  // * Resizing window
  // * CRUD
  // ...
  reset(event = {}, immediate = false) {
    var _a5;
    (_a5 = super.reset) == null ? void 0 : _a5.call(this);
    this.refresh(immediate);
  }
  immediateReset() {
    this.reset({}, true);
  }
  //endregion
  //region Scheduler hooks
  render() {
    this.client.getConfig("svgCanvas");
  }
  //endregion
};
__publicField(Dependencies, "$name", "Dependencies");
/**
 * Fired when dependencies are rendered
 * @on-owner
 * @event dependenciesDrawn
 */
//region Config
__publicField(Dependencies, "configurable", {
  /**
   * The CSS class to add to a dependency line when hovering over it
   * @config {String}
   * @default
   * @private
   */
  overCls: "b-sch-dependency-over",
  /**
   * The CSS class applied to dependency lines
   * @config {String}
   * @default
   * @private
   */
  baseCls: "b-sch-dependency",
  /**
   * The CSS class applied to a too narrow dependency line (to hide markers)
   * @config {String}
   * @default
   * @private
   */
  noMarkerCls: "b-sch-dependency-markerless",
  /**
   * The CSS class representing a delete icon shown when clicking a dependency line
   * @config {String}
   * @default
   */
  removeIconCls: "b-icon b-icon-remove-circle",
  /**
   * SVG path definition used as marker (arrow head) for the dependency lines.
   * Should fit in a viewBox that is 9 x 6.
   *
   * ```javascript
   * const scheduler = new Scheduler({
   *     features : {
   *         dependencies : {
   *             // Circular marker
   *             markerDef : 'M 2,3 a 3,3 0 1,0 6,0 a 3,3 0 1,0 -6,0'
   *         }
   *     }
   * });
   * ```
   *
   * @config {String}
   * @default 'M3,0 L3,6 L9,3 z'
   */
  markerDef: null,
  /**
   * Radius (in px) used to draw arcs where dependency line segments connect. Specify it to get a rounded look.
   * The radius will during drawing be reduced as needed on a per segment basis to fit lines.
   *
   * ```javascript
   * const scheduler = new Scheduler({
   *     features : {
   *         dependencies : {
   *             // Round the corner where line segments connect, similar to 'border-radius: 5px'
   *             radius : 5
   *         }
   *     }
   * });
   * ```
   *
   * <div class="note">Using a radius slightly degrades dependency rendering performance. If your app displays
   * a lot of dependencies, it might be worth taking this into account when deciding if you want to use radius
   * or not</div>
   *
   * @config {Number}
   */
  radius: null,
  /**
   * Renderer function, supply one if you want to manipulate the {@link DomConfig} object used to draw a
   * dependency line between two assignments.
   *
   * ```javascript
   * const scheduler = new Scheduler({
   *     features : {
   *         dependencies : {
   *             renderer({ domConfig, fromAssignmentRecord : from, toAssignmentRecord : to }) {
   *                 // Add a custom CSS class to dependencies between important assignments
   *                 domConfig.class.important = from.important || to.important;
   *                 domConfig.class.veryImportant = from.important && to.important;
   *             }
   *         }
   *     }
   * }
   * ```
   *
   * @prp {Function}
   * @param {Object} renderData
   * @param {DomConfig} renderData.domConfig that will be used to create the dependency line, can be manipulated by the renderer
   * @param {Scheduler.model.DependencyModel} renderData.dependencyRecord The dependency being rendered
   * @param {Scheduler.model.AssignmentModel} renderData.fromAssignmentRecord Drawing line from this assignment
   * @param {Scheduler.model.AssignmentModel} renderData.toAssignmentRecord Drawing line to this assignment
   * @param {Object[]} renderData.points A collection of points making up the line segments for the dependency line.
   *   Read-only in the renderer, any manipulation should be done to `domConfig`
   * @param {Core.helper.util.Rectangle} renderData.fromBox Bounds for the fromAssignment's element
   * @param {Core.helper.util.Rectangle} renderData.toBox Bounds for the toAssignment's element
   * @param {'top'|'right'|'bottom'|'left'} renderData.fromSide Drawn from this side of the fromAssignment
   * @param {'top'|'right'|'bottom'|'left'} renderData.toSide Drawn to this side of the fromAssignment
   * @returns {void}
   *
   * @category Rendering
   */
  renderer: null,
  /**
   * Specify `true` to highlight incoming and outgoing dependencies when hovering an event.
   * @prp {Boolean}
   */
  highlightDependenciesOnEventHover: null,
  /**
   * Specify `false` to not enable simple deletion of dependencies by clicking on them.
   * @config {Boolean}
   * @default
   */
  enableDelete: true,
  /**
   * Specify `false` to prevent dependencies from being drawn during scroll, for smoother scrolling in schedules
   * with lots of dependencies. Dependencies will be drawn when scrolling stops instead.
   * @prp {Boolean}
   * @default
   */
  drawOnScroll: true,
  /**
   * Specify `false` to prevent dependencies from being redrawn during event interaction, such as dragging,
   * resizing or rescheduling through other means, including when events animate into place. This makes
   * interaction smoother, but dependency lines will not be updated until the interaction is finished.
   * @prp {Boolean}
   * @default
   */
  drawOnEventInteraction: true,
  /**
   * The clickable/touchable width of the dependency line in pixels. Setting this to a number greater than 1 will
   * draw an invisible but clickable line along the same path as the dependency line, making it easier to click.
   * The tradeoff is that twice as many lines will be drawn, which can affect performance.
   * @prp {Number}
   */
  clickWidth: null,
  /**
   * By default, the refresh of dependencies is buffered by 10 milliseconds so that multiple changes
   * which may cause the dependency lines to become invalid are coalesced into one refresh. This is more
   * efficient, but may mean the dependency lines may lag behind expectations when moving a pointer.
   *
   * Set this to `true` to update dependency lines immediately upon any change which causes them
   * to require an update.
   * @prp {Boolean}
   * @default false
   * @private
   */
  immediateRefresh: null,
  /**
   * *Experimental* - This setting only applies when using dependencies with the nested events feature. In such
   * scenarios, enabling this config will cause the dependency lines to, when the algorithm determines it is
   * possible, be drawn around parent events, instead of through them.
   *
   * {@note}
   * Note that enabling this feature increases the complexity of dependency drawing, and it does have a negative
   * impact on performance.
   * {/@note}
   *
   * @prp {Boolean}
   */
  drawAroundParents: null
});
__publicField(Dependencies, "delayable", {
  doRefresh: 20,
  rafRefresh: "raf"
});
Dependencies._$name = "Dependencies";
GridFeatureManager.registerFeature(Dependencies, false, ["Scheduler", "ResourceHistogram"]);

// ../Scheduler/lib/Scheduler/view/DependencyEditor.js
var DependencyEditor = class extends Popup {
  static get defaultConfig() {
    return {
      items: [],
      axisLock: "flexible"
    };
  }
  processWidgetConfig(widget) {
    const { dependencyEditFeature } = this;
    if (widget.ref === "lagField" && !dependencyEditFeature.showLagField) {
      return false;
    }
    if (widget.ref === "deleteButton" && !dependencyEditFeature.showDeleteButton) {
      return false;
    }
    return super.processWidgetConfig(widget);
  }
  afterShow(...args) {
    const { deleteButton } = this.widgetMap;
    if (deleteButton) {
      deleteButton.hidden = !this.record.isPartOfStore();
    }
    super.afterShow(...args);
  }
  onInternalKeyDown(event) {
    this.trigger("keyDown", { event });
    super.onInternalKeyDown(event);
  }
};
__publicField(DependencyEditor, "$name", "DependencyEditor");
DependencyEditor._$name = "DependencyEditor";

// ../Scheduler/lib/Scheduler/feature/DependencyEdit.js
var _DependencyEdit = class _DependencyEdit extends InstancePlugin {
  static get pluginConfig() {
    return {
      chain: [
        "populateDependencyMenu"
      ]
    };
  }
  //endregion
  //region Init & destroy
  construct(client, config) {
    const me = this;
    client.dependencyEdit = me;
    super.construct(client, config);
    if (!client.features.dependencies) {
      throw new Error("Dependencies feature required when using DependencyEdit");
    }
  }
  doDestroy() {
    var _a5;
    this.clientListenersDetacher();
    (_a5 = this.editor) == null ? void 0 : _a5.destroy();
    super.doDestroy();
  }
  //endregion
  //region Editing
  changeEditorConfig(config) {
    const me = this, { autoClose, cls, client } = me;
    return ObjectHelper.assign({
      owner: client,
      align: "b-t",
      id: `${client.id}-dependency-editor`,
      autoShow: false,
      anchor: true,
      scrollAction: "realign",
      constrainTo: globalThis,
      autoClose,
      cls
    }, config);
  }
  //endregion
  //region Save
  get isValid() {
    return Object.values(this.editor.widgetMap).every((field) => {
      if (!field.name || field.hidden) {
        return true;
      }
      return field.isValid !== false;
    });
  }
  get values() {
    const values = {};
    this.editor.eachWidget((widget) => {
      if (!widget.name || widget.hidden)
        return;
      values[widget.name] = widget.value;
    }, true);
    return values;
  }
  /**
   * Template method, intended to be overridden. Called before the dependency record has been updated.
   * @param {Scheduler.model.DependencyModel} dependencyRecord The dependency record
   *
   **/
  onBeforeSave(dependencyRecord) {
  }
  /**
   * Template method, intended to be overridden. Called after the dependency record has been updated.
   * @param {Scheduler.model.DependencyModel} dependencyRecord The dependency record
   *
   **/
  onAfterSave(dependencyRecord) {
  }
  /**
   * Updates record being edited with values from the editor
   * @private
   */
  updateRecord(dependencyRecord) {
    const { values } = this;
    if (values.lag) {
      values.lagUnit = values.lag.unit;
      values.lag = values.lag.magnitude;
    }
    if ("type" in values) {
      dependencyRecord.fromSide != null && (values.fromSide = null);
      dependencyRecord.toSide != null && (values.toSide = null);
    }
    ObjectHelper.cleanupProperties(values, true);
    dependencyRecord.set(values);
  }
  //endregion
  //region Events
  onPopupKeyDown({ event }) {
    if (event.key === "Enter" && this.saveAndCloseOnEnter && event.target.tagName.toLowerCase() === "input") {
      event.preventDefault();
      this.onSaveClick();
    }
  }
  onHide() {
    if (!this.isApplyingChanges) {
      this.afterCancel();
    }
  }
  async onSaveClick() {
    const me = this;
    me.isApplyingChanges = true;
    if (await me.save()) {
      me.afterSave();
      await me.editor.hide();
      me.isApplyingChanges = false;
    } else {
      me.isApplyingChanges = false;
    }
  }
  async onDeleteClick() {
    const me = this;
    me.isApplyingChanges = true;
    if (await me.deleteDependency()) {
      me.afterDelete();
    }
    await me.editor.hide();
    me.isApplyingChanges = false;
  }
  onCancelClick() {
    this.editor.hide();
  }
  afterSave() {
  }
  afterDelete() {
  }
  afterCancel() {
  }
  //region Editing
  // Called from editDependency() to actually show the editor
  internalShowEditor(dependencyRecord) {
    const me = this, {
      client,
      lastPointerDown
    } = me, { dependencies } = client.features, editor = me.getEditor(dependencyRecord), targetElement = (dependencies == null ? void 0 : dependencies.getElementForDependency(dependencyRecord)) || (lastPointerDown == null ? void 0 : lastPointerDown.target) || client.timeAxisSubGridElement, targetRect = Rectangle.from(targetElement);
    me.loadRecord(dependencyRecord);
    client.trigger("beforeDependencyEditShow", {
      dependencyEdit: me,
      dependencyRecord,
      editor
    });
    let alignSpec;
    if (lastPointerDown) {
      Object.defineProperties(lastPointerDown, {
        offsetX: { configurable: true, value: lastPointerDown.clientX - targetRect.x },
        offsetY: { configurable: true, value: lastPointerDown.clientY - targetRect.y }
      });
      alignSpec = {
        target: lastPointerDown,
        align: "t50-b0"
      };
    } else {
      alignSpec = {
        target: targetElement,
        align: "c-c"
      };
    }
    const result = editor.showBy(alignSpec), labelled = [];
    let labelWidth = 0;
    editor.eachWidget((widget) => {
      const { labelElement, element } = widget;
      if (labelElement) {
        if (labelElement.getBoundingClientRect().top < element.getBoundingClientRect().top) {
          return false;
        }
        widget.labelWidth = null;
        labelWidth = Math.max(labelWidth, labelElement.offsetWidth);
        labelled.push(widget);
      }
    });
    labelled.forEach((widget) => widget.labelWidth = labelWidth);
    return result;
  }
  /**
   * Opens a popup to edit the passed dependency.
   * @param {Scheduler.model.DependencyModel} dependencyRecord The dependency to edit
   * @return {Promise} A Promise that yields `true` after the editor is shown
   * or `false` if some application logic vetoed the editing (see `beforeDependencyEdit` in the docs).
   */
  async editDependency(dependencyRecord) {
    const me = this, { client } = me;
    if (client.readOnly || dependencyRecord.readOnly || /**
     * Fires on the owning Scheduler or Gantt widget before an dependency is displayed in the editor.
     * This may be listened for to allow an application to take over dependency editing duties. Return `false` to
     * stop the default editing UI from being shown or a `Promise` yielding `true` or `false` for async vetoing.
     * @event beforeDependencyEdit
     * @on-owner
     * @param {Scheduler.view.Scheduler} source The scheduler or Gantt instance
     * @param {Scheduler.feature.DependencyEdit} dependencyEdit The dependencyEdit feature
     * @param {Scheduler.model.DependencyModel} dependencyRecord The record about to be shown in the editor.
     * @preventable
     * @async
     */
    await client.trigger("beforeDependencyEdit", { dependencyEdit: me, dependencyRecord }) === false) {
      return false;
    }
    await this.internalShowEditor(dependencyRecord);
    return true;
  }
  //endregion
  //region Save
  /**
   * Gets an editor instance. Creates on first call, reuses on consecutive
   * @internal
   * @returns {Scheduler.view.DependencyEditor} Editor popup
   */
  getEditor() {
    var _a5, _b2, _c2;
    const me = this;
    let { editor } = me;
    if (editor) {
      return editor;
    }
    editor = me.editor = DependencyEditor.new({
      dependencyEditFeature: me,
      autoShow: false,
      anchor: true,
      scrollAction: "realign",
      constrainTo: globalThis,
      autoClose: me.autoClose,
      cls: me.cls,
      rootElement: me.client.rootElement,
      internalListeners: {
        keydown: me.onPopupKeyDown,
        hide: me.onHide,
        thisObj: me
      }
    }, me.editorConfig);
    if (editor.items.length === 0) {
      console.warn("Editor configured without any `items`");
    }
    editor.eachWidget((widget) => {
      const ref = widget.ref || widget.id;
      if (ref && !me[ref]) {
        me[ref] = widget;
      }
    });
    (_a5 = me.saveButton) == null ? void 0 : _a5.ion({ click: "onSaveClick", thisObj: me });
    (_b2 = me.deleteButton) == null ? void 0 : _b2.ion({ click: "onDeleteClick", thisObj: me });
    (_c2 = me.cancelButton) == null ? void 0 : _c2.ion({ click: "onCancelClick", thisObj: me });
    return me.editor;
  }
  //endregion
  //region Delete
  /**
   * Sets fields values from record being edited
   * @private
   */
  loadRecord(dependency) {
    const me = this;
    me.fromNameField.value = dependency.fromEvent.name;
    me.toNameField.value = dependency.toEvent.name;
    if (me.lagField) {
      me.lagField.value = new Duration(dependency.lag, dependency.lagUnit);
    }
    me.editor.record = me.dependencyRecord = dependency;
  }
  //endregion
  //region Stores
  /**
   * Saves the changes (applies them to record if valid, if invalid editor stays open)
   * @private
   * @fires beforeDependencySave
   * @fires beforeDependencyAdd
   * @fires afterDependencySave
   * @returns {*}
   */
  async save() {
    var _a5;
    const me = this, { client, dependencyRecord } = me;
    if (!dependencyRecord || !me.isValid) {
      return;
    }
    const { dependencyStore, values } = me;
    if (client.trigger("beforeDependencySave", {
      dependencyRecord,
      values
    }) !== false) {
      me.onBeforeSave(dependencyRecord);
      me.updateRecord(dependencyRecord);
      if (dependencyStore && !dependencyRecord.stores.length) {
        if (client.trigger("beforeDependencyAdd", { dependencyRecord, dependencyEdit: me }) === false) {
          return;
        }
        dependencyStore.add(dependencyRecord);
      }
      await ((_a5 = client.project) == null ? void 0 : _a5.commitAsync());
      client.trigger("afterDependencySave", { dependencyRecord });
      me.onAfterSave(dependencyRecord);
    }
    return dependencyRecord;
  }
  /**
   * Delete dependency being edited
   * @private
   * @fires beforeDependencyDelete
   */
  async deleteDependency() {
    var _a5;
    const { client, editor, dependencyRecord } = this;
    if (client.trigger("beforeDependencyDelete", { dependencyRecord }) !== false) {
      if (editor.containsFocus) {
        editor.revertFocus();
      }
      client.dependencyStore.remove(dependencyRecord);
      await ((_a5 = client.project) == null ? void 0 : _a5.commitAsync());
      return true;
    }
    return false;
  }
  get dependencyStore() {
    return this.client.dependencyStore;
  }
  //endregion
  //region Events
  onActivateEditor({ dependency, event }) {
    if (!this.disabled) {
      this.lastPointerDown = event;
      this.editDependency(dependency);
    }
  }
  //endregion
  updateTriggerEvent(triggerEvent) {
    var _a5;
    (_a5 = this.clientListenersDetacher) == null ? void 0 : _a5.call(this);
    if (triggerEvent) {
      this.clientListenersDetacher = this.client.ion({
        [triggerEvent]: this.onActivateEditor,
        thisObj: this
      });
    }
  }
  //region Context menu
  populateDependencyMenu({ dependencyRecord, items: items2 }) {
    if (!this.client.readOnly && !this.disabled) {
      items2.edit = {
        text: "L{DependencyEdit.Edit dependency}",
        localeClass: this,
        icon: "b-icon b-icon-edit",
        weight: 100,
        disabled: dependencyRecord.readOnly,
        onItem: () => {
          this.editDependency(dependencyRecord);
        }
      };
    }
  }
  //endregion
};
//region Config
__publicField(_DependencyEdit, "$name", "DependencyEdit");
__publicField(_DependencyEdit, "configurable", {
  /**
   * True to hide this editor if a click is detected outside it (defaults to true)
   * @config {Boolean}
   * @default
   * @category Editor
   */
  autoClose: true,
  /**
   * True to save and close this panel if ENTER is pressed in one of the input fields inside the panel.
   * @config {Boolean}
   * @default
   * @category Editor
   */
  saveAndCloseOnEnter: true,
  /**
   * True to show a delete button in the form.
   * @config {Boolean}
   * @default
   * @category Editor widgets
   */
  showDeleteButton: true,
  /**
   * The event that shall trigger showing the editor. Defaults to `dependencydblclick`, set to empty string or
   * `null` to disable editing of dependencies.
   * @prp {String}
   * @default
   * @category Editor
   */
  triggerEvent: "dependencydblclick",
  /**
   * True to show the lag field for the dependency
   * @config {Boolean}
   * @default
   * @category Editor widgets
   */
  showLagField: false,
  dependencyRecord: null,
  /**
   * Default editor configuration, used to configure the Popup.
   * @config {PopupConfig}
   * @category Editor
   */
  editorConfig: {
    title: "L{Edit dependency}",
    localeClass: _DependencyEdit,
    closable: true,
    defaults: {
      localeClass: _DependencyEdit
    },
    items: {
      /**
       * Reference to the source task name field
       * @member {Core.widget.DisplayField} fromNameField
       * @readonly
       */
      fromNameField: {
        type: "display",
        weight: 100,
        label: "L{From}"
      },
      /**
       * Reference to the target task name field
       * @member {Core.widget.DisplayField} toNameField
       * @readonly
       */
      toNameField: {
        type: "display",
        weight: 200,
        label: "L{To}"
      },
      /**
       * Reference to the dependency type field
       * @member {Core.widget.Combo} typeField
       * @readonly
       */
      typeField: {
        type: "combo",
        weight: 300,
        label: "L{Type}",
        name: "type",
        editable: false,
        valueField: "id",
        displayField: "name",
        localizeDisplayFields: true,
        buildItems: function() {
          const dialog = this.parent;
          return Object.keys(DependencyModel.Type).map((type) => ({
            id: DependencyModel.Type[type],
            name: dialog.L(type),
            localeKey: type
          }));
        }
      },
      /**
       * Reference to the lag field
       * @member {Core.widget.DurationField} lagField
       * @readonly
       */
      lagField: {
        type: "duration",
        weight: 400,
        label: "L{Lag}",
        name: "lag",
        allowNegative: true,
        highlightExternalChange: false
      }
    },
    bbar: {
      defaults: {
        localeClass: _DependencyEdit
      },
      items: {
        foo: {
          type: "widget",
          cls: "b-label-filler"
        },
        /**
         * Reference to the save button, if used
         * @member {Core.widget.Button} saveButton
         * @readonly
         */
        saveButton: {
          color: "b-green",
          text: "L{Save}"
        },
        /**
         * Reference to the delete button, if used
         * @member {Core.widget.Button} deleteButton
         * @readonly
         */
        deleteButton: {
          color: "b-gray",
          text: "L{Delete}"
        },
        /**
         * Reference to the cancel button, if used
         * @member {Core.widget.Button} cancelButton
         * @readonly
         */
        cancelButton: {
          color: "b-gray",
          text: "L{Object.Cancel}"
        }
      }
    }
  }
});
var DependencyEdit = _DependencyEdit;
DependencyEdit._$name = "DependencyEdit";
GridFeatureManager.registerFeature(DependencyEdit, false);

// ../Scheduler/lib/Scheduler/feature/DependencyMenu.js
var DependencyMenu = class extends ContextMenuBase {
  static get pluginConfig() {
    const config = super.pluginConfig;
    config.chain.push("populateDependencyMenu");
    return config;
  }
  //endregion
  //region Events
  /**
   * This event fires on the owning Scheduler before the context menu is shown for a dependency.
   * Allows manipulation of the items to show in the same way as in `processItems`. Returning `false` from a listener
   * prevents the menu from being shown.
   * @event dependencyMenuBeforeShow
   * @on-owner
   * @preventable
   * @param {Scheduler.view.Scheduler} source
   * @param {Object<String,MenuItemConfig>} items Menu item configs
   * @param {Scheduler.model.DependencyModel} dependencyRecord Dependency record for which the menu was triggered
   * @param {HTMLElement} element
   * @param {MouseEvent} [event] Pointer event which triggered the context menu (if any)
   */
  /**
   * This event fires on the owning Scheduler when an item is selected in the context menu.
   * @event dependencyMenuItem
   * @on-owner
   * @param {Scheduler.view.Scheduler} source
   * @param {Scheduler.model.DependencyModel} dependencyRecord Dependency record for which the menu was triggered
   * @param {Core.widget.MenuItem} item The menu item
   * @param {HTMLElement} element The dependency element
   */
  /**
   * This event fires on the owning Scheduler after showing the context menu for an event
   * @event dependencyMenuShow
   * @on-owner
   * @param {Scheduler.view.Scheduler} source
   * @param {Core.widget.Menu} menu The menu
   * @param {Scheduler.model.DependencyModel} dependencyRecord Dependency record for which the menu was triggered
   * @param {HTMLElement} element The dependency element
   */
  //endregion
  getDataFromEvent(event) {
    const data = super.getDataFromEvent(event), { dependencies } = this.client.features, element = event.target.closest(`.${dependencies.baseCls}`);
    if (element) {
      const dependencyRecord = dependencies.resolveDependencyRecord(element);
      return Object.assign(data, {
        element,
        dependencyRecord
      });
    }
  }
  shouldShowMenu(eventParams) {
    return eventParams.dependencyRecord;
  }
  populateDependencyMenu({ items: items2, dependencyRecord }) {
    items2.delete = {
      disabled: dependencyRecord.readOnly,
      hidden: this.client.readOnly
    };
  }
  // This generates the fixed, unchanging part of the items and is only called once
  // to generate the baseItems of the feature.
  // The dynamic parts which are set by populateDependencyMenu have this merged into them.
  changeItems(items2) {
    return Objects.merge({
      delete: {
        text: "L{DependencyEdit.Delete}",
        icon: "b-icon b-icon-trash",
        cls: "b-separator",
        weight: 500,
        onItem({ dependencyRecord }) {
          dependencyRecord.remove();
        }
      }
    }, items2);
  }
};
//region Config
__publicField(DependencyMenu, "$name", "DependencyMenu");
/**
 * @member {SchedulerMenuContext} menuContext
 * An informational object containing contextual information about the last activation of the context menu.
 * @readonly
 */
__publicField(DependencyMenu, "configurable", {
  /**
   * A function called before displaying the menu that allows manipulations of its items.
   * Returning `false` from this function prevents the menu being shown.
   *
   * ```javascript
   * features         : {
   *    dependencyMenu : {
   *         processItems({ items, dependencyRecord }) {
   *             // Add or hide existing items here as needed
   *             items.myAction = {
   *                 text   : 'Cool action',
   *                 icon   : 'b-fa b-fa-fw b-fa-ban',
   *                 onItem : () => console.log(`Clicked ${dependencyRecord.name}`),
   *                 weight : 1000 // Move to end
   *             };
   *
   *            if (!dependencyRecord.allowDelete) {
   *                 items.delete.hidden = true;
   *             }
   *         }
   *     }
   * },
   * ```
   *
   * @config {Function}
   * @param {Object} context An object with information about the menu being shown.
   * @param {Scheduler.feature.DependencyMenu} context.feature A reference to this feature.
   * @param {Event} context.domEvent The initiating event.
   * @param {Number[]} context.point The client `X` and `Y` position of the initiating event.
   * @param {HTMLElement} context.targetElement The target to which the menu is being applied.
   * @param {Scheduler.model.DependencyModel} context.dependencyRecord The dependency record.
   * @param {Object<String,MenuItemConfig|Boolean>} context.items An object containing the
   *   {@link Core.widget.MenuItem menu item} configs keyed by their id.
   * @returns {Boolean|null} Returning `false` from this function prevents the menu being shown.
   * @preventable
   */
  processItems: null,
  /**
   * This is a preconfigured set of items used to create the default context menu. The default options are
   * listed at the top of the page.
   *
   * To remove existing items, set corresponding keys `null`:
   *
   * ```javascript
   * const scheduler = new Scheduler({
   *     features : {
   *         dependencyMenu : {
   *             items : {
   *                 delete   : null
   *             }
   *         }
   *     }
   * });
   * ```
   *
   * See the feature config in the above example for details.
   *
   * @config {Object<String,MenuItemConfig|Boolean|null>} items
   */
  type: "dependency"
});
DependencyMenu.featureClass = "";
DependencyMenu._$name = "DependencyMenu";
GridFeatureManager.registerFeature(DependencyMenu, VersionHelper.checkVersion("core", "7.0", ">="), ["Scheduler", "Gantt"]);

// ../Scheduler/lib/Scheduler/feature/ScheduleContext.js
var ScheduleContext = class extends InstancePlugin.mixin(Delayable_default) {
  constructor() {
    super(...arguments);
    __publicField(this, "cursorTrackingSuspended", 0);
  }
  /**
   * The contextual information about which cell was clicked on and highlighted.
   *
   * When the {@link Scheduler.view.Scheduler#property-viewPreset} is changed (such as when zooming)
   * the context is cleared and the highlight is removed.
   *
   * @member {Object} context
   * @property {Scheduler.view.TimelineBase} context.source The owning Scheduler
   * @property {Date} context.date Date at mouse position
   * @property {Scheduler.model.TimeSpan} context.tick A record which encapsulates the time axis tick clicked on.
   * @property {Number} context.tickIndex The index of the time axis tick clicked on.
   * @property {Date} context.tickStartDate The start date of the current time axis tick
   * @property {Date} context.tickEndDate The end date of the current time axis tick
   * @property {Grid.row.Row} context.row Clicked row (in horizontal mode only)
   * @property {Number} context.index Index of clicked resource
   * @property {Scheduler.model.ResourceModel} context.resourceRecord Resource record
   * @property {MouseEvent} context.event Browser event
   */
  construct(client, config) {
    super.construct(client, config);
    const { triggerEvent } = this, listeners = {
      datachange: "syncContextElement",
      timeaxisviewmodelupdate: "onTimeAxisViewModelUpdate",
      presetchange: "clear",
      thisObj: this
    };
    if (triggerEvent === "mouseover") {
      listeners.timelineContextChange = "onTimelineContextChange";
    } else {
      if (triggerEvent === "click" || triggerEvent === "mousedown") {
        listeners.schedulecontextmenu = "onScheduleContextGesture";
      }
      listeners[`schedule${triggerEvent}`] = "onScheduleContextGesture";
    }
    client.ion(listeners);
    client.rowManager.ion({
      rowheight: "syncContextElement",
      thisObj: this
    });
  }
  changeTriggerEvent(triggerEvent) {
    if (triggerEvent === "hover" || triggerEvent === "mousemove") {
      triggerEvent = "mouseover";
    }
    return triggerEvent;
  }
  changeWidget(widget, oldWidget) {
    if (widget) {
      return Widget.reconfigure(oldWidget, {
        ...widget,
        owner: this.client,
        positioned: true,
        appendTo: this.client.timeAxisSubGridElement,
        hidden: true,
        showAnimation: false
      }, this);
    }
    oldWidget.destroy();
  }
  get element() {
    var _a5;
    if (!this._element) {
      this._element = ((_a5 = this.widget) == null ? void 0 : _a5.element) || DomHelper.createElement({
        parent: this.client.timeAxisSubGridElement
      });
      this.element.classList.add("b-schedule-selected-tick");
    }
    return this._element;
  }
  // Handle the Client's own timelineContextChange event which it maintains on mousemove
  onTimelineContextChange({ context }) {
    this.context = context;
  }
  // Handle the scheduleclick or eventclick Scheduler events if we re not using mouseover
  onScheduleContextGesture(context) {
    this.context = context;
  }
  onTimeAxisViewModelUpdate({ source: timeAxisViewModel }) {
    var _a5;
    if (timeAxisViewModel.timeAxis.includes((_a5 = this.context) == null ? void 0 : _a5.tick)) {
      this.syncContextElement();
    } else {
      this.clear();
    }
  }
  /**
   * Clears the current visible context element
   */
  clear() {
    this.context = null;
  }
  changeContext(context) {
    var _a5;
    if (!((_a5 = this.widget) == null ? void 0 : _a5.query((widget) => widget.floating && widget.isVisible))) {
      if (!this.shareWithEvent && this.hasEvent(context)) {
        return null;
      }
      return !this.activateOnEvent && (context == null ? void 0 : context.eventElement) ? null : context || null;
    }
  }
  updateActivateOnEvent(activateOnEvent) {
    const me = this;
    if (activateOnEvent) {
      const { triggerEvent, client } = me;
      me.shareWithEvent = true;
      if (triggerEvent !== "mouseover") {
        client.ion({
          name: "scheduleContextTrigger",
          [`${client.scheduledEventName}${triggerEvent}`]: "onScheduleContextGesture",
          thisObj: me
        });
      }
    } else {
      me.shareWithEvent = me.config.shareWithEvent;
      me.detachListeners("scheduleContextTrigger");
    }
  }
  /**
   * Utility function to see if there is an event bar coinciding with the passed timeline context
   * @param {TimelineContext} context The tick context to check
   * @returns {Boolean} `true` if there is an event for the passed tick context
   * @internal
   */
  hasEvent(context) {
    var _a5;
    return Boolean(context && ((_a5 = this.client.eventStore.getEvents({
      resourceRecord: context.resourceRecord,
      startDate: context.tickStartDate,
      endDate: context.tickEndDate
    })) == null ? void 0 : _a5.length));
  }
  updateContext(context, oldContext) {
    this.syncContextElement();
  }
  syncContextElement() {
    const me = this, {
      client,
      element,
      widget,
      context
    } = me, {
      isVertical
    } = client, row = context && (isVertical ? client.rowManager.rows[0] : client.getRowFor(context.resourceRecord));
    if (context && row && this.enabled && !context.domEvent.buttons) {
      const me2 = this, { renderer } = me2, { style } = element, {
        tickStartDate,
        tickEndDate,
        resourceRecord
      } = context, renderData = client.currentOrientation.getTimeSpanRenderData({
        startDate: tickStartDate,
        endDate: tickEndDate,
        startDateMS: tickStartDate.getTime(),
        endDateMS: tickEndDate.getTime()
      }, resourceRecord);
      let top, width, height;
      if (isVertical) {
        top = renderData.top;
        width = renderData.resourceWidth;
        height = renderData.height;
      } else {
        top = row.top;
        width = renderData.width;
        height = row.height;
      }
      context.index = row.index;
      element.classList.add("b-schedule-selected-tick");
      element.dataset.resourceId = resourceRecord.id;
      element.classList.toggle("b-contains-widget", Boolean(element.querySelector(".b-widget")));
      if (widget) {
        widget.hide(false);
        if (!context.eventRecord) {
          widget.setConfig({
            width,
            height,
            timelineContext: context,
            x: renderData.left,
            y: top
          });
          widget.show({ animate: false });
        }
      } else if (element) {
        style.width = `${width}px`;
        style.height = `${height}px`;
        DomHelper.setTranslateXY(element, renderData.left, top);
        element.innerHTML = "";
        renderer && me2.callback(renderer, me2, [context, element]);
        style.display = "";
      }
    } else {
      (widget == null ? void 0 : widget.hide(false)) || (element.style.display = "none");
    }
  }
};
__publicField(ScheduleContext, "$name", "ScheduleContext");
__publicField(ScheduleContext, "configurable", {
  /**
   * The pointer event type to use to update the context. May be `'hover'` to highlight the
   * tick context when moving the mouse across the timeline.
   * @config {'click'|'hover'|'contextmenu'|'mousedown'}
   * @default
   */
  triggerEvent: "click",
  /**
   * A function (or the name of a function) which may mutate the contents of the context overlay
   * element which tracks the active resource/tick context. Use this when simple, decorative or
   * informational HTML is required.
   *
   * Use the {@link #config-widget} option to show a widget with interaction in a tick cell.
   *
   * @config {String|Function}
   * @param {TimelineContext} context The context being highlighted.
   * @param {HTMLElement} element The context highlight element. This will be empty each time.
   * @returns {void}
   */
  renderer: null,
  /**
   * The widget shown in timeline ticks. The widget will be sized and positioned to fit the tick.
   *
   * Before being shown, it will be primed with a property called `timelineContext` which is a {@link TimelineContext}
   * which describes exactly which tick is active.
   *
   * The widget's {@link Core.widget.Widget#property-owner} will be the Scheduler.
   *
   * The {@link Core.widget.Widget#property-owner}'s {@link Scheduler.view.Scheduler#property-timelineContext}
   * will also reference the current context.
   *
   * @member {Core.widget.Widget} widget
   * @property {TimelineContext} timelineContext The timeline context for which the widget was activated.
   * @readonly
   */
  /**
   * A widget config describing the widget to show in the context tick.
   * @config {ContainerItemConfig}
   */
  widget: {
    $config: ["lazy", "nullify"],
    value: null
  },
  /**
   * The active context.
   * @member {TimelineContext} timelineContext
   * @readonly
   */
  context: {
    $config: {
      // Reject non-changes so that when using mousemove, we only update the context
      // when it changes.
      equal(c1, c2) {
        return (c1 == null ? void 0 : c1.index) === (c2 == null ? void 0 : c2.index) && (c1 == null ? void 0 : c1.tickParentIndex) === (c2 == null ? void 0 : c2.tickParentIndex) && !(((c1 == null ? void 0 : c1.tickStartDate) || 0) - ((c2 == null ? void 0 : c2.tickStartDate) || 0)) && (c1 == null ? void 0 : c1.eventRecord) === (c2 == null ? void 0 : c2.eventRecord);
      }
    }
  },
  /**
   * By default, the ScheduleContext feature hides its widget or element when the
   * pointer is over event/task bar.
   *
   * Set this property to `true` to activate when over an event/task bar.
   * @prp {Boolean} activateOnEvent
   * @default false
   */
  activateOnEvent: null,
  /**
   * By default, the ScheduleContext feature hides its widget or element when the
   * pointer is over a tick in which an event/task bar exists.
   *
   * Set this property to `true` to activate when an event/task bar also exists in the context tick.
   * @prp {Boolean} shareWithEvent
   * @default false
   */
  shareWithEvent: null
});
ScheduleContext.featureClass = "b-scheduler-context";
ScheduleContext._$name = "ScheduleContext";
GridFeatureManager.registerFeature(ScheduleContext, false, ["Scheduler"]);

// ../Scheduler/lib/Scheduler/feature/EventCopyPaste.js
var EventCopyPaste = class extends CopyPasteBase.mixin(AttachToProjectMixin_default) {
  constructor() {
    super(...arguments);
    // Used in events to separate events from different features from each other
    __publicField(this, "entityName", "event");
  }
  static get pluginConfig() {
    return {
      assign: [
        "copyEvents",
        "pasteEvents"
      ],
      chain: [
        "populateEventMenu",
        "populateScheduleMenu",
        "onEventDataGenerated"
      ]
    };
  }
  construct(scheduler, config) {
    super.construct(scheduler, config);
    scheduler.ion({
      eventClick: "onEventClick",
      scheduleClick: "onScheduleClick",
      projectChange: () => {
        this.clearClipboard();
        this._cellClickedContext = null;
      },
      thisObj: this
    });
  }
  get scheduler() {
    return this.client;
  }
  attachToEventStore(eventStore) {
    super.attachToEventStore(eventStore);
    delete this._eventClickedContext;
  }
  onEventDataGenerated(eventData) {
    const { assignmentRecord } = eventData;
    if (assignmentRecord) {
      eventData.cls["b-cut-item"] = assignmentRecord.meta.isCut;
    }
  }
  onEventClick(context) {
    this._cellClickedContext = null;
    this._eventClickedContext = context;
  }
  onScheduleClick(context) {
    this._cellClickedContext = context;
    this._eventClickedContext = null;
  }
  isActionAvailable({ event, actionName }) {
    return super.isActionAvailable(...arguments) && Boolean(event.target.closest(this.client.eventContainerSelector));
  }
  async copy() {
    return await this.copyEvents();
  }
  async cut() {
    return await this.copyEvents(void 0, true);
  }
  async paste() {
    return await this.pasteEvents();
  }
  /**
   * Copy events (when using single assignment mode) or assignments (when using multi assignment mode) to clipboard to
   * paste later
   * @fires beforeCopy
   * @fires copy
   * @param {Scheduler.model.EventModel[]|Scheduler.model.AssignmentModel[]} [records] Pass records to copy them,
   * leave out to copying current selection
   * @param {Boolean} [isCut] Copies by default, pass `true` to cut instead
   * @category Edit
   * @on-owner
   */
  async copyEvents(records = this.scheduler.selectedAssignments, isCut = false) {
    var _a5;
    const me = this, { scheduler } = me;
    if (scheduler.splitFrom) {
      return scheduler.splitFrom.features.eventCopyPaste.copyEvents(records, isCut);
    }
    if (!(records == null ? void 0 : records.length)) {
      return;
    }
    const { isEventModel } = records[0];
    if (isCut) {
      records = records.filter((r) => isEventModel ? !r.readOnly : !r.event.readOnly);
    } else {
      records = records.slice();
    }
    const assignmentRecords = isEventModel ? records.flatMap((r) => r.assignments) : records, eventRecords = assignmentRecords.length ? assignmentRecords.map((a) => a.event) : records;
    if (!assignmentRecords.length && (!me.allowNoAssignments || !eventRecords.length)) {
      return;
    }
    const clipboardData = (_a5 = await me.writeToClipboard({ assignmentRecords, eventRecords }, isCut)) == null ? void 0 : _a5.data;
    if (!clipboardData) {
      return;
    }
    scheduler.trigger("copy", {
      originalAssignmentRecords: assignmentRecords,
      originalEventRecords: eventRecords,
      assignmentRecords: clipboardData.assignmentRecords,
      eventRecords: clipboardData.eventRecords,
      isCut,
      entityName: me.entityName
    });
    me.refreshClient();
    me._focusedEventOnCopy = me._eventClickedContext;
  }
  async beforeCopy({ data: { assignmentRecords, eventRecords }, isCut }) {
    return await this.scheduler.trigger(
      "beforeCopy",
      { assignmentRecords, eventRecords, isCut, entityName: this.entityName }
    );
  }
  // Called from Clipboardable when cutData changes
  handleCutData({ source }) {
    var _a5;
    const me = this;
    if (source !== me && ((_a5 = me.cutData) == null ? void 0 : _a5.length)) {
      const { assignmentRecords, eventRecords } = me.cutData[0];
      if (assignmentRecords == null ? void 0 : assignmentRecords.length) {
        me.scheduler.assignmentStore.remove(assignmentRecords);
      }
      if (eventRecords == null ? void 0 : eventRecords.length) {
        me.scheduler.eventStore.remove(eventRecords);
      }
    }
  }
  /**
   * Called from Clipboardable after writing a non-string value to the clipboard
   * @param eventRecords
   * @returns {string}
   * @private
   */
  stringConverter({ eventRecords }) {
    const rows = [];
    for (const event of eventRecords) {
      rows.push(this.eventToStringFields.map((field) => {
        const value2 = event[field];
        if (value2 instanceof Date) {
          return DateHelper.format(value2, this.dateFormat);
        }
        return value2;
      }).join("	"));
    }
    return rows.join("\n");
  }
  // Called from Clipboardable for each cut out record
  setIsCut({ assignmentRecords }, isCut) {
    assignmentRecords.forEach((assignment) => {
      assignment.meta.isCut = isCut;
    });
    this.refreshClient();
  }
  // This function will be called on both copy and paste actions
  cloneData({ data, isPaste }) {
    const me = this, { eventStore } = me, eventModel = eventStore.modelClass, allEvents = /* @__PURE__ */ new Set(), events = [], assignments = [], shouldCopyEvents = eventStore.usesSingleAssignment || me.copyPasteAction === "clone", copyEvent = (event, assignment) => {
      var _a5, _b2;
      const copy = event.copy(null, true);
      if (isPaste) {
        copy.traverse((e) => {
          e.name = me.generateNewName(e, event, eventStore);
        });
      }
      copy.meta.originalResourceId = isPaste ? event.meta.originalResourceId : (_a5 = assignment == null ? void 0 : assignment.resourceId) != null ? _a5 : event.resourceId;
      if ((_b2 = copy.fieldMap) == null ? void 0 : _b2.resourceIds.persist) {
        copy.resourceIds = [];
      }
      return copy;
    };
    let { assignmentRecords, eventRecords } = data;
    if (isPaste && !assignmentRecords && !eventRecords) {
      if ((data == null ? void 0 : data.length) && data[0].isModel) {
        eventRecords = data.map((record) => new eventModel({ ...record.toJSON(), [eventModel.idField]: void 0 }));
        return { eventRecords, assignmentRecords: [] };
      } else {
        return;
      }
    }
    if (isPaste && shouldCopyEvents) {
      events.push(...eventRecords.map((r) => copyEvent(r)));
    } else {
      let source = assignmentRecords;
      if (!(source == null ? void 0 : source.length) && me.allowNoAssignments) {
        source = eventRecords;
      }
      for (const record of source) {
        let event = record.isEventModel ? record : eventStore.getById(record.eventId);
        if (allEvents.has(event)) {
          continue;
        }
        allEvents.add(event);
        if (shouldCopyEvents || record.isEventModel) {
          event = copyEvent(event, assignmentRecords == null ? void 0 : assignmentRecords.find((a) => a.eventId === event.id));
          if (event.constraintDate) {
            event.constraintDate = null;
          }
          events.push(event);
        } else {
          assignments.push(record.copy());
        }
      }
    }
    return {
      assignmentRecords: assignments,
      eventRecords: events
    };
  }
  /**
   * Paste events or assignments to specified date and resource
   * @fires beforePaste
   * @fires paste
   * @param {Date} [date] Date where the events or assignments will be pasted
   * @param {Scheduler.model.ResourceModel} [resourceRecord] Resource to assign the pasted events or assignments to
   * @category Edit
   * @on-owner
   */
  async pasteEvents(date2, resourceRecord) {
    var _a5, _b2, _c2, _d, _e, _f, _g, _h, _i;
    const me = this, {
      scheduler,
      copyPasteAction,
      entityName,
      unifiedPaste,
      _cellClickedContext,
      _eventClickedContext
    } = me, {
      eventStore,
      assignmentStore,
      resourceStore
    } = scheduler;
    if (arguments.length === 0) {
      if (_cellClickedContext) {
        date2 = _cellClickedContext.date;
        resourceRecord = _cellClickedContext.resourceRecord;
      } else if (me._focusedEventOnCopy !== _eventClickedContext) {
        date2 = _eventClickedContext.eventRecord.startDate;
        resourceRecord = _eventClickedContext.resourceRecord;
      }
    }
    if (resourceRecord) {
      resourceRecord = resourceRecord.$original;
    }
    if (scheduler.splitFrom) {
      return scheduler.splitFrom.features.eventCopyPaste.pasteEvents(date2, resourceRecord);
    }
    const { data, isCut, originalData } = (_a5 = await me.readFromClipboard({ resourceRecord, date: date2 })) != null ? _a5 : {}, records = ((_b2 = data == null ? void 0 : data.assignmentRecords) == null ? void 0 : _b2.length) ? data.assignmentRecords : data == null ? void 0 : data.eventRecords, assignmentRecords = [], eventRecords = [];
    let toFocus, prevDate, prevResource;
    if (!(records == null ? void 0 : records.length)) {
      return;
    }
    for (let assignment of records) {
      let event;
      if (assignment.isEventModel) {
        event = assignment;
        assignment = null;
      } else {
        event = (_c2 = assignment.event) != null ? _c2 : eventStore.getById(assignment.eventId);
        assignment.meta.isCut = false;
      }
      if (!eventRecords.includes(event)) {
        if (date2) {
          if (unifiedPaste && prevDate) {
            date2 = new Date(date2.getTime() + (event.startDate - prevDate));
          }
          prevDate = event.startDate;
          event.startDate = date2;
          if (event.constraintDate) {
            event.constraintDate = null;
          }
        }
        if (!isCut) {
          eventStore.add(event);
          resourceRecord != null ? resourceRecord : resourceRecord = resourceStore.getById(event.meta.originalResourceId);
        }
        eventRecords.push(event);
      }
      if (resourceRecord) {
        const currentResource = resourceStore.getById((_e = (_d = assignment == null ? void 0 : assignment.resourceId) != null ? _d : event.resourceId) != null ? _e : event.meta.originalResourceId);
        if (unifiedPaste && prevResource && !scheduler.isCalendar) {
          const indexDiff = resourceStore.indexOf(currentResource) - resourceStore.indexOf(prevResource);
          resourceRecord = (_f = resourceStore.getAt(resourceStore.indexOf(resourceRecord) + indexDiff)) != null ? _f : resourceStore.last;
        }
        if (!resourceRecord.readOnly && !event.isAssignedTo(resourceRecord)) {
          assignment = event.assign(resourceRecord);
          event.traverse((child) => child.assign(resourceRecord), true);
        }
        if (currentResource && !currentResource.readOnly && resourceRecord !== currentResource && !eventStore.usesSingleAssignment && (isCut || copyPasteAction === "clone")) {
          event.unassign(currentResource);
        }
        prevResource = currentResource;
      } else if (!isCut && scheduler.isCalendar && assignment && !event.isAssignedTo(assignment.resourceId)) {
        assignmentStore.add(assignment);
      }
      if (assignment) {
        assignmentRecords.push(assignment);
        toFocus = assignment;
      } else {
        toFocus = assignmentStore.last;
      }
    }
    if (!resourceRecord) {
      resourceRecord = (_i = (_g = assignmentRecords == null ? void 0 : assignmentRecords[0]) == null ? void 0 : _g.resource) != null ? _i : (_h = eventRecords == null ? void 0 : eventRecords[0]) == null ? void 0 : _h.resource;
    }
    scheduler.trigger("paste", {
      eventRecords,
      assignmentRecords,
      pastedEventRecords: eventRecords,
      // DEPRECATE
      originalEventRecords: originalData == null ? void 0 : originalData.eventRecords,
      originalAssignmentRecords: originalData == null ? void 0 : originalData.assignmentRecords,
      resourceRecord,
      date: date2,
      isCut,
      entityName
    });
    if (scheduler.isSchedulerBase) {
      const detacher2 = scheduler.ion({
        renderEvent({ assignmentRecord }) {
          if (assignmentRecord === toFocus) {
            scheduler.navigateTo(assignmentRecord, { scrollIntoView: false });
            detacher2();
          }
        }
      });
    }
    if (isCut) {
      await me.clearClipboard();
    }
  }
  // Called from Clipboardable before finishing the internal clipboard read
  async beforePaste({ originalData, data: { assignmentRecords, eventRecords }, resourceRecord, isCut, date: date2 }) {
    var _a5;
    const { scheduler } = this, originalAssignmentRecords = originalData == null ? void 0 : originalData.assignmentRecords;
    if (!resourceRecord) {
      resourceRecord = (_a5 = originalAssignmentRecords == null ? void 0 : originalAssignmentRecords[0]) == null ? void 0 : _a5.resource;
    }
    const eventData = {
      assignmentRecords,
      eventRecords,
      resourceRecord,
      originalAssignmentRecords,
      originalEventRecords: originalData == null ? void 0 : originalData.eventRecords,
      date: date2,
      isCut,
      entityName: this.entityName
    };
    let reason;
    if (resourceRecord == null ? void 0 : resourceRecord.readOnly) {
      reason = "resourceReadOnly";
    }
    if (!reason && !scheduler.allowOverlap) {
      const recordsToCheck = (assignmentRecords == null ? void 0 : assignmentRecords.length) ? assignmentRecords : eventRecords;
      for (const record of recordsToCheck) {
        const event = record.isAssignmentModel ? record.event : record;
        if (!scheduler.isDateRangeAvailable(
          date2 != null ? date2 : event.startDate,
          date2 ? DateHelper.add(date2, event.duration, event.durationUnit) : event.endDate,
          isCut ? event : null,
          resourceRecord != null ? resourceRecord : record.resource
        )) {
          reason = "overlappingEvents";
          break;
        }
      }
    }
    if (reason) {
      scheduler.trigger("pasteNotAllowed", {
        ...eventData,
        reason
      });
      return false;
    }
    return await this.scheduler.trigger("beforePaste", eventData);
  }
  /**
   * Called from Clipboardable after reading from clipboard, and it is determined that the clipboard data is
   * "external"
   * @param json
   * @returns {Object}
   * @private
   */
  stringParser(clipboardData) {
    const { eventStore, assignmentStore } = this.scheduler, { modifiedRecords: eventRecords } = this.setFromStringData(clipboardData, true, eventStore, this.eventToStringFields), assignmentRecords = [];
    for (const event of eventRecords) {
      const assignment = assignmentStore.createRecord({ eventId: event.id });
      assignment.event = event;
      assignmentRecords.push(assignment);
    }
    return { eventRecords, assignmentRecords };
  }
  populateEventMenu({ assignmentRecord, items: items2 }) {
    var _a5;
    const me = this, { scheduler } = me;
    if (me.disabled) {
      return;
    }
    items2.copyEvent = {
      text: "L{copyEvent}",
      localeClass: me,
      icon: "b-icon b-icon-copy",
      cls: "b-separator",
      weight: 110,
      onItem: () => {
        const assignments = scheduler.isAssignmentSelected(assignmentRecord) ? scheduler.selectedAssignments : [assignmentRecord];
        me.copyEvents(assignments);
      }
    };
    if (!scheduler.readOnly) {
      items2.cutEvent = {
        text: "L{cutEvent}",
        localeClass: me,
        icon: "b-icon b-icon-cut",
        weight: 120,
        disabled: assignmentRecord.readOnly || ((_a5 = assignmentRecord.event) == null ? void 0 : _a5.readOnly),
        onItem: () => {
          const assignments = scheduler.isAssignmentSelected(assignmentRecord) ? scheduler.selectedAssignments : [assignmentRecord];
          me.copyEvents(assignments, true);
        }
      };
    }
  }
  populateScheduleMenu({ items: items2, resourceRecord }) {
    const me = this, { scheduler } = me;
    if (!me.disabled && !scheduler.readOnly && me.hasClipboardData() !== false) {
      items2.pasteEvent = {
        text: "L{pasteEvent}",
        localeClass: me,
        icon: "b-icon b-icon-paste",
        disabled: scheduler.resourceStore.count === 0 && !me.allowNoAssignments || (resourceRecord == null ? void 0 : resourceRecord.readOnly),
        weight: 110,
        internalListeners: { item: me.onScheduleMenuPasteClick, thisObj: me }
      };
    }
  }
  onScheduleMenuPasteClick({ date: date2, resourceRecord }) {
    this.pasteEvents(date2, resourceRecord);
  }
  refreshClient() {
    this.scheduler.refreshWithTransition();
  }
};
__publicField(EventCopyPaste, "$name", "EventCopyPaste");
__publicField(EventCopyPaste, "configurable", {
  /**
   * The field to use as the name field when updating the name of copied records
   * @config {String}
   * @default
   */
  nameField: "name",
  /**
   * How to handle a copy paste operation when the host uses multi assignment. Either:
   *
   * - `'clone'`  - The default, clone the copied event, assigning the clone to the target resource.
   * - `'assign'` - Add an assignment for the existing event to the target resource.
   *
   * For single assignment mode, it always uses the `'clone'` behaviour.
   *
   * @config {'clone'|'assign'}
   * @default
   */
  copyPasteAction: "clone",
  /**
   * When copying events (or assignments), data will be sent to the clipboard as a tab (`\t`) and new-line (`\n`)
   * separated string with field values for fields present in this config (in specified order). The default
   * included fields are (in this order):
   * * name
   * * startDate
   * * endDate
   * * duration
   * * durationUnit
   * * allDay
   * To override, provide your own array of fields:
   * ```javascript
   * new Scheduler({
   *     features : {
   *         eventCopyPaste : {
   *             eventToStringFields : [
   *                'name',
   *                'startDate',
   *                'endDate',
   *                'percentDone'
   *             ]
   *         }
   *     }
   * });
   * ```
   * <div class="note">Please note that this config is both used for **converting** events to a string value and
   * is also used to **parse** a string value to events.</div>
   * @config {Array<String>}
   */
  eventToStringFields: ["name", "startDate", "endDate", "duration", "durationUnit", "allDay"],
  /**
   * By default, pasting of multiple events will spread out according to the pattern it was copied. Set to `false`
   * to paste into the same resource and same date.
   * @config {Boolean}
   * @default
   */
  unifiedPaste: true,
  allowedDataSources: ["EventCopyPaste", "RowCopyPaste", "TaskCopyPaste"],
  // Used by Calendar
  allowNoAssignments: false
});
EventCopyPaste.featureClass = "b-event-copypaste";
EventCopyPaste._$name = "EventCopyPaste";
GridFeatureManager.registerFeature(EventCopyPaste, true, "Scheduler");

// ../Scheduler/lib/Scheduler/feature/EventDrag.js
var EventDrag = class extends DragBase.mixin(TransactionalFeature_default) {
  static get configurable() {
    return {
      /**
       * Template used to generate drag tooltip contents.
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         eventDrag : {
       *             tooltipTemplate({eventRecord, startText}) {
       *                 return `${eventRecord.name}: ${startText}`
       *             }
       *         }
       *     }
       * });
       * ```
       * @config {Function} tooltipTemplate
       * @param {Object} data Tooltip data
       * @param {Scheduler.model.EventModel} data.eventRecord
       * @param {Scheduler.model.ResourceModel} data.newResource The new resource record
       * @param {Boolean} data.valid Currently over a valid drop target or not
       * @param {Date} data.startDate New start date
       * @param {Date} data.endDate New end date
       * @returns {String}
       */
      /**
       * Set to `true` to only allow dragging in one direction (based on initial movement)
       * @prp {Boolean}
       * @default false
       */
      singleDirection: null,
      /**
       * Set to true to only allow dragging events within the same resource.
       * @prp {Boolean}
       * @default
       */
      constrainDragToResource: false,
      /**
       * Set to true to only allow dragging events to different resources, and disallow rescheduling by dragging.
       * @prp {Boolean}
       * @default
       */
      constrainDragToTimeSlot: false,
      /**
       * A CSS selector specifying elements outside the scheduler element which are valid drop targets.
       * @config {String}
       */
      externalDropTargetSelector: null,
      /**
       * An empty function by default, but provided so that you can perform custom validation on the item being
       * dragged. This function is called during the drag and drop process and also after the drop is made.
       * Return `true` if the new position is valid, `false` to prevent the drag.
       *
       * ```javascript
       * features : {
       *     eventDrag : {
       *         validatorFn({ eventRecords, newResource }) {
       *             const
       *                 task  = eventRecords[0],
       *                 valid = newResource.role === task.resource.role;
       *
       *             return {
       *                 valid   : newResource.role === task.resource.role,
       *                 message : valid ? '' : 'Resource role does not match required role for this task'
       *             };
       *         }
       *     }
       * }
       * ```
       * @param {Object} context A drag drop context object
       * @param {Date} context.startDate New start date
       * @param {Date} context.endDate New end date
       * @param {Scheduler.model.AssignmentModel[]} context.assignmentRecords Assignment records which were dragged
       * @param {Scheduler.model.EventModel[]} context.eventRecords Event records which were dragged
       * @param {Scheduler.model.ResourceModel} context.newResource New resource record
       * @param {Scheduler.model.EventModel} context.targetEventRecord Currently hovering this event record
       * @param {Event} event The event object
       * @returns {Boolean|Object} `true` if this validation passes, `false` if it does not.
       *
       * Or an object with 2 properties: `valid` -  Boolean `true`/`false` depending on validity,
       * and `message` - String with a custom error message to display when invalid.
       * @config {Function}
       */
      validatorFn: (context, event) => {
      },
      /**
       * The `this` reference for the validatorFn
       * @config {Object}
       */
      validatorFnThisObj: null,
      /**
       * When the host Scheduler is `{@link Scheduler.view.mixin.EventSelection#config-multiEventSelect}: true`
       * then, there are two modes of dragging *within the same Scheduler*.
       *
       * Non unified means that all selected events are dragged by the same number of resource rows.
       *
       * Unified means that all selected events are collected together and dragged as one, and are all dropped
       * on the same targeted resource row at the same targeted time.
       * @prp {Boolean}
       * @default false
       */
      unifiedDrag: null,
      /**
       * A hook that allows manipulating the position the drag proxy snaps to. Manipulate the `snapTo` property
       * to alter snap position.
       *
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         eventDrag : {
       *             snapToPosition({ eventRecord, snapTo }) {
       *                 if (eventRecord.late) {
       *                     snapTo.x = 400;
       *                 }
       *             }
       *         }
       *     }
       * });
       * ```
       *
       * @config {Function}
       * @param {Object} context
       * @param {Scheduler.model.AssignmentModel} context.assignmentRecord Dragged assignment
       * @param {Scheduler.model.EventModel} context.eventRecord Dragged event
       * @param {Scheduler.model.ResourceModel} context.resourceRecord Currently over this resource
       * @param {Date} context.startDate Start date for current position
       * @param {Date} context.endDate End date for current position
       * @param {Object} context.snapTo
       * @param {Number} context.snapTo.x X to snap to
       * @param {Number} context.snapTo.y Y to snap to
       * @returns {void}
       */
      snapToPosition: null,
      /**
       * Set it to `true` to be able to snap events to resources while dragging events,
       * i.e. vertical row snap in horizontal mode and vice versa.
       * If you want to customize the number of pixels from the dragged event that will trigger
       * the snapping behavior to the new resource, you can set the `threshold` property.
       * It works only if `{@link Scheduler.view.mixin.TimelineDateMapper#config-snap}: true`.
       *
       * ```javascript
       * const scheduler = new Scheduler({
       *     ...
       *     snap     : true,
       *     features : {
       *         eventDrag : {
       *             snapToResource : {
       *                 threshold : 30
       *             }
       *         }
       *     },
       *     ...
       * });
       * ```
       *
       *
       * @prp {Boolean|Object}
       * @property {Number} [threshold] Amount of pixels from dragged event to the edge of new resource
       * that will trigger the snapping behaviour to the new resource.
       * @default false
       */
      snapToResource: VersionHelper.checkVersion("core", "7.0", ">="),
      /**
       * A modifier key (CTRL, SHIFT, ALT, META) that when pressed will copy an event instead of moving it. Set to
       * empty string to disable copying. Defaults to "CTRL" which is the Ctrl-key for Windows, and Meta-key for
       * MacOS.
       * @prp {'CTRL'|'ALT'|'SHIFT'|'META'|''}
       * @default
       */
      copyKey: "CTRL",
      /**
       * Event can be copied two ways: either by adding new assignment to an existing event ('assignment'), or
       * by copying the event itself ('event'). 'auto' mode will pick 'event' for a single-assignment mode (when
       * event has `resourceId` field) and 'assignment' mode otherwise.
       * @prp {'auto'|'assignment'|'event'}
       * @default
       */
      copyMode: "auto",
      /**
       * Mode of the current drag drop operation.
       * @member {'move'|'copy'}
       * @readonly
       */
      mode: "move",
      /**
       * Set this to `true` to always copy the event on drag drop operation.
       * @prp {Boolean}
       * @default false
       */
      alwaysCopy: null
    };
  }
  //endregion
  changeMode(value2) {
    const { dragData, copyMode } = this;
    if ((copyMode === "event" || copyMode === "auto" || copyMode === "assignment" && !this.scheduler.eventStore.usesSingleAssignment) && (!dragData || dragData.eventRecords.every((r) => !r.isRecurring))) {
      return value2;
    }
  }
  updateMode(mode) {
    if (this.dragData) {
      if (mode === "copy") {
        this.setCopying();
      } else {
        this.setMoving();
      }
      this.client.trigger("eventDragModeChange", { mode });
    }
  }
  setCopying() {
    const { dragData } = this;
    if (!dragData) {
      return;
    }
    if (!dragData.eventBarCopies.some((el) => el.isConnected)) {
      dragData.eventBarCopies.forEach((el) => {
        el.classList.add("b-drag-proxy-copy");
        el.classList.remove("b-hidden");
        dragData.context.grabbedParent.appendChild(el);
        el.retainElement = true;
      });
    } else {
      dragData.eventBarCopies.forEach((el) => {
        el.classList.remove("b-hidden");
      });
    }
  }
  setMoving() {
    const { dragData } = this;
    if (!dragData) {
      return;
    }
    dragData.eventBarCopies.forEach((el) => {
      el.classList.add("b-hidden");
    });
  }
  //region Events
  /**
   * This event is fired on the owning Scheduler after the event drag operation completes, but before changing any data.
   * It allows implementer to use asynchronous validation/finalization by setting `context.async = true`
   * in the listener, for example, to show a confirmation popup, make async data request etc.
   * In such case, implementer need to call the `context.finalize()` method manually:
   *
   * ```javascript
   *  scheduler.on('beforeeventdropfinalize', ({ context }) => {
   *      context.async = true;
   *      setTimeout(() => {
   *          // `true` to perform the drop, `false` to ignore it
   *          context.finalize(true);
   *      }, 1000);
   *  })
   * ```
   *
   * For synchronous one-time validation, simply set `context.valid` to true or false.
   * ```javascript
   *  scheduler.on('beforeeventdropfinalize', ({ context }) => {
   *      context.valid = false;
   *  })
   * ```
   * @event beforeEventDropFinalize
   * @on-owner
   * @param {Scheduler.view.Scheduler} source Scheduler instance
   * @param {Object} context
   * @param {DropData} context.dropData Information about the drop points for dragged events/assignments.
   * @param {Boolean} context.async Set to `true` to not finalize the drag-drop operation immediately (e.g. to wait for user confirmation)
   * @param {Scheduler.model.EventModel[]} context.eventRecords Event records being dragged
   * @param {Scheduler.model.AssignmentModel[]} context.assignmentRecords Assignment records being dragged
   * @param {Scheduler.model.EventModel} context.targetEventRecord The event record (if any) where the drop happened
   * @param {Scheduler.model.ResourceModel} context.newResource Resource record for drop target
   * @param {Boolean} context.valid Set this to `false` to abort the drop immediately.
   * @param {Function} context.finalize Call this method after an **async** finalization flow, to finalize the drag-drop operation. This method accepts one
   * argument: pass `true` to update records, or `false` to ignore changes
   * @param {MouseEvent} domEvent Browser event
   */
  /**
   * Fired on the owning Scheduler after event drop
   * @event afterEventDrop
   * @on-owner
   * @param {Scheduler.view.Scheduler} source
   * @param {Scheduler.model.AssignmentModel[]} assignmentRecords
   * @param {Scheduler.model.EventModel[]} eventRecords
   * @param {Boolean} valid
   * @param {Object} context
   * @param {MouseEvent} domEvent Browser event
   */
  /**
   * Fired on the owning Scheduler when an event is dropped
   * @event eventDrop
   * @on-owner
   * @param {Scheduler.view.Scheduler} source
   * @param {Scheduler.model.EventModel[]} eventRecords
   * @param {Scheduler.model.AssignmentModel[]} assignmentRecords
   * @param {HTMLElement} externalDropTarget The HTML element dropped upon, if drop happened on a valid external drop target
   * @param {Boolean} isCopy
   * @param {Object} context
   * @param {Scheduler.model.EventModel} context.targetEventRecord Event record for drop target
   * @param {Scheduler.model.ResourceModel} context.newResource Resource record for drop target
   * @param {MouseEvent} domEvent Browser event
   */
  /**
   * Fired on the owning Scheduler before event dragging starts. Return `false` to prevent the action.
   * @event beforeEventDrag
   * @on-owner
   * @preventable
   * @param {Scheduler.view.Scheduler} source Scheduler instance
   * @param {Scheduler.model.EventModel} eventRecord Event record the drag starts from
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record the drag starts from
   * @param {Scheduler.model.EventModel[]} eventRecords Event records being dragged
   * @param {Scheduler.model.AssignmentModel[]} assignmentRecords Assignment records being dragged
   * @param {MouseEvent} domEvent Browser event
   */
  /**
   * Fired on the owning Scheduler when event dragging starts
   * @event eventDragStart
   * @on-owner
   * @param {Scheduler.view.Scheduler} source Scheduler instance
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record the drag starts from
   * @param {Scheduler.model.EventModel[]} eventRecords Event records being dragged
   * @param {Scheduler.model.AssignmentModel[]} assignmentRecords Assignment records being dragged
   * @param {MouseEvent} domEvent Browser event
   */
  /**
   * Fired on the owning Scheduler when event is dragged
   * @event eventDrag
   * @on-owner
   * @param {Scheduler.view.Scheduler} source Scheduler instance
   * @param {Scheduler.model.EventModel[]} eventRecords Event records being dragged
   * @param {Scheduler.model.AssignmentModel[]} assignmentRecords Assignment records being dragged
   * @param {Date} startDate Start date for the current location
   * @param {Date} endDate End date for the current location
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record the drag started from
   * @param {Scheduler.model.ResourceModel} newResource Resource at the current location
   * @param {Object} context
   * @param {Boolean} context.valid Set this to `false` to signal that the current drop position is invalid.
   * @param {MouseEvent} domEvent Browser event
   */
  /**
   * Fired on the owning Scheduler after an event drag operation has been aborted
   * @event eventDragAbort
   * @on-owner
   * @param {Scheduler.view.Scheduler} source Scheduler instance
   * @param {Scheduler.model.EventModel[]} eventRecords Event records being dragged
   * @param {Scheduler.model.AssignmentModel[]} assignmentRecords Assignment records being dragged
   * @param {MouseEvent} domEvent Browser event
   */
  /**
   * Fired on the owning Scheduler after an event drag operation regardless of the operation being cancelled or not
   * @event eventDragReset
   * @on-owner
   * @param {Scheduler.view.Scheduler} source Scheduler instance
   */
  //endregion
  //region Data layer
  // Deprecated. Use this.client instead
  get scheduler() {
    return this.client;
  }
  //endregion
  //#region Drag lifecycle
  onBeforeDragStart(...params) {
    let result = super.onBeforeDragStart(...params);
    if (result) {
      if (!this.canStartFeatureTransaction) {
        this.client.trigger("eventDragPrevented", { reason: "revision" });
        result = false;
      } else {
        this.startFeatureTransaction().then();
      }
    }
    return result;
  }
  onDragAbortFinalized(...params) {
    super.onDragAbortFinalized(...params);
    this.rejectFeatureTransaction();
  }
  async finalize(...params) {
    await super.finalize(...params);
    await this.finishFeatureTransaction();
  }
  onAfterDragStart(event) {
    const me = this, { drag } = me, { context: { element } } = event;
    if (me.singleDirection) {
      drag.lockY = event.initialDirection === "horizontal";
      drag.lockX = !drag.lockY;
    }
    super.onAfterDragStart(event);
    me.handleKeyDownOrMove(event.event);
    me.keyEventDetacher = EventHelper.on({
      // In case we drag event between scheduler focused event gets moved and focus
      // moves to the body. We only need to read the key from this event
      element: DomHelper.getRootElement(element),
      keydown: me.handleKeyDownOrMove,
      keyup: me.handleKeyUp,
      thisObj: me
    });
  }
  onDragReset(event) {
    var _a5;
    super.onDragReset(event);
    (_a5 = this.keyEventDetacher) == null ? void 0 : _a5.call(this);
    this.mode = "move";
  }
  onDrop(event) {
    var _a5;
    (_a5 = this.dragData.eventBarCopies) == null ? void 0 : _a5.forEach((el) => el.remove());
    return super.onDrop(event);
  }
  //#endregion
  //region Drag events
  getDraggableElement(el) {
    return el == null ? void 0 : el.closest(this.drag.targetSelector);
  }
  resolveEventRecord(eventElement, client = this.client) {
    return client.resolveEventRecord(eventElement);
  }
  isElementDraggable(el, event) {
    var _a5;
    const me = this, { client } = me, eventElement = me.getDraggableElement(el);
    if (!eventElement || me.disabled || client.readOnly) {
      return false;
    }
    if (el.matches('[class$="-handle"]')) {
      return false;
    }
    const eventRecord = me.resolveEventRecord(eventElement, client);
    if (!eventRecord || !eventRecord.isDraggable || eventRecord.readOnly) {
      return false;
    }
    const prevented = ((_a5 = client[`is${me.capitalizedEventName}ElementDraggable`]) == null ? void 0 : _a5.call(
      client,
      eventElement,
      eventRecord,
      el,
      event
    )) === false;
    return !prevented;
  }
  getTriggerParams(dragData) {
    const { assignmentRecords, eventRecords, resourceRecord, browserEvent: domEvent } = dragData;
    return {
      // `context` is now private, but used in WebSocketHelper
      context: dragData,
      eventRecords,
      resourceRecord,
      assignmentRecords,
      domEvent
    };
  }
  getGroupedToStoreResources(dragData) {
    if (dragData.resourcesInStore) {
      return dragData.resourcesInStore;
    }
    const fromScheduler = this.client, fromResourceStore = fromScheduler.isVertical ? fromScheduler.resourceStore : fromScheduler.store;
    return dragData.resourcesInStore = [...new Set(fromResourceStore.getAllDataRecords().map((r) => r.$original))].filter((r) => r.isLeaf);
  }
  getIndexDiff(dragData) {
    const me = this, fromScheduler = me.client, toScheduler = me.currentOverClient, isCrossScheduler = fromScheduler !== toScheduler, { isVertical } = toScheduler, fromResourceStore = fromScheduler.isVertical ? fromScheduler.resourceStore : fromScheduler.store, toResourceStore = isVertical ? toScheduler.resourceStore : toScheduler.store, {
      resourceRecord: fromResource,
      newResource: toResource
    } = dragData;
    let indexDiff;
    if (isCrossScheduler) {
      indexDiff = toResourceStore.indexOf(toResource) - fromResourceStore.indexOf(fromResource.$original);
    } else if (me.constainDragToResource) {
      indexDiff = 0;
    } else if (isVertical && toResourceStore.isGrouped) {
      const resourcesInStore = me.getGroupedToStoreResources(dragData);
      indexDiff = resourcesInStore.indexOf(fromResource.$original) - resourcesInStore.indexOf(toResource);
    } else {
      indexDiff = fromResourceStore.indexOf(fromResource.$original) - fromResourceStore.indexOf(toResource);
    }
    return indexDiff;
  }
  getNewResource(dragData, originalResourceRecord, indexDiff) {
    const me = this, fromScheduler = me.client, toScheduler = me.currentOverClient, isCrossScheduler = fromScheduler !== toScheduler, { isVertical } = toScheduler, fromResourceStore = fromScheduler.isVertical ? fromScheduler.resourceStore : fromScheduler.store, toResourceStore = isVertical ? toScheduler.resourceStore : toScheduler.store;
    let { newResource } = dragData;
    if (!isCrossScheduler) {
      if (indexDiff !== 0) {
        let newIndex;
        if (isVertical && toResourceStore.isGrouped) {
          const resourcesInStore = me.getGroupedToStoreResources(dragData);
          newIndex = Math.max(
            Math.min(
              resourcesInStore.indexOf(originalResourceRecord) - indexDiff,
              resourcesInStore.length - 1
            ),
            0
          );
          newResource = resourcesInStore[newIndex];
        } else {
          newIndex = Math.max(
            Math.min(
              fromResourceStore.indexOf(originalResourceRecord) - indexDiff,
              fromResourceStore.count - 1
            ),
            0
          );
          newResource = fromResourceStore.getAt(newIndex);
          if (newResource.isSpecialRow) {
            newResource = fromResourceStore.getNext(newResource, false, true) || fromResourceStore.getPrevious(newResource, false, true);
          }
        }
        newResource = newResource == null ? void 0 : newResource.$original;
      } else {
        newResource = originalResourceRecord;
      }
    } else {
      const draggedEventResourceIndex = fromResourceStore.indexOf(originalResourceRecord);
      newResource = toResourceStore.getAt(draggedEventResourceIndex + indexDiff) || newResource;
    }
    return newResource;
  }
  getDropData(dragData) {
    const indexDiff = this.getIndexDiff(dragData);
    return {
      events: dragData.eventRecords.map((eventRecord) => {
        return {
          eventRecord,
          ...this.getEventNewStartEndDates(eventRecord, dragData.timeDiff, this.currentOverClient)
        };
      }),
      assignments: dragData.assignmentRecords.map((assignmentRecord) => {
        return {
          assignmentRecord,
          resourceRecord: this.getNewResource(dragData, assignmentRecord.resource, indexDiff)
        };
      })
    };
  }
  triggerBeforeEventDropFinalize(eventType, eventData, client) {
    eventData.context.dropData = this.getDropData(eventData.context);
    super.triggerBeforeEventDropFinalize(eventType, eventData, client);
  }
  triggerBeforeEventDrag(eventType, event) {
    return this.client.trigger(eventType, event);
  }
  triggerEventDrag(dragData, start) {
    this.client.trigger("eventDrag", Object.assign(this.getTriggerParams(dragData), {
      startDate: dragData.startDate,
      endDate: dragData.endDate,
      newResource: dragData.newResource
    }));
  }
  triggerDragStart(dragData) {
    this.client.navigator.skipNextClick = true;
    this.client.trigger("eventDragStart", this.getTriggerParams(dragData));
  }
  triggerDragAbort(dragData) {
    this.client.trigger("eventDragAbort", this.getTriggerParams(dragData));
  }
  triggerDragAbortFinalized(dragData) {
    this.client.trigger("eventDragAbortFinalized", this.getTriggerParams(dragData));
  }
  triggerAfterDrop(dragData, valid) {
    const me = this;
    me.currentOverClient.trigger("afterEventDrop", Object.assign(me.getTriggerParams(dragData), {
      valid
    }));
    if (!valid) {
      const { assignmentStore, eventStore } = me.client, needRefresh = me.dragData.initialAssignmentsState.find(({
        resource,
        assignment
      }, i) => {
        var _a5;
        return !assignmentStore.includes(assignment) || !eventStore.includes(assignment.event) || resource.id !== ((_a5 = me.dragData.assignmentRecords[i]) == null ? void 0 : _a5.resourceId);
      });
      if (needRefresh) {
        me.client.refresh();
      }
    }
    me.client.setTimeout(() => me.client.navigator.skipNextClick = false, 10);
  }
  handleKeyDownOrMove(event) {
    var _a5, _b2;
    const me = this;
    if (me.mode !== "copy" && (me.alwaysCopy || me.isCtrlKeyPress(event) || event.key && EventHelper.specialKeyFromEventKey(event.key) === ((_a5 = me.copyKey) == null ? void 0 : _a5.toLowerCase()) || event[`${(_b2 = me.copyKey) == null ? void 0 : _b2.toLowerCase()}Key`])) {
      me.mode = "copy";
    }
  }
  handleKeyUp(event) {
    if (this.isCtrlKeyPress(event) || EventHelper.specialKeyFromEventKey(event.key) === this.copyKey.toLowerCase()) {
      this.mode = "move";
    }
  }
  isCtrlKeyPress(event) {
    return this.copyKey === "CTRL" && event.key === (BrowserHelper.ctrlKey === "ctrlKey" ? "Ctrl" : "Meta");
  }
  //endregion
  //region Finalization & validation
  /**
   * Checks if an event can be dropped on the specified position.
   * @private
   * @returns {Boolean} Valid (true) or invalid (false)
   */
  isValidDrop(dragData) {
    const {
      newResource,
      resourceRecord,
      browserEvent
    } = dragData, sourceRecord = dragData.draggedEntities[0], { target } = browserEvent;
    if (!newResource) {
      return !this.constrainDragToTimeline && this.externalDropTargetSelector ? Boolean(target.closest(this.externalDropTargetSelector)) : false;
    }
    if (newResource.isSpecialRow || newResource.readOnly) {
      return false;
    }
    if (resourceRecord.$original !== newResource) {
      return !sourceRecord.event.resources.includes(newResource);
    }
    return true;
  }
  checkDragValidity(dragData, event) {
    var _a5, _b2;
    const me = this, scheduler = me.currentOverClient, { newResource } = dragData;
    let result;
    if (newResource == null ? void 0 : newResource.readOnly) {
      return false;
    }
    if (newResource && me.hasOverlaps()) {
      result = {
        valid: false,
        message: me.L("L{eventOverlapsExisting}")
      };
    } else {
      result = me.validatorFn.call(
        me.validatorFnThisObj || me,
        dragData,
        event
      );
    }
    if (!result || result.valid) {
      result = (_b2 = (_a5 = scheduler["checkEventDragValidity"]) == null ? void 0 : _a5.call(scheduler, dragData, event)) != null ? _b2 : result;
    }
    return result;
  }
  hasOverlaps() {
    const me = this, { dragData, currentOverClient } = me, { startDate, origStart } = dragData, msDiff = startDate - origStart, indexDiff = me.getIndexDiff(me.dragData), { bufferIsUnavailableTime } = currentOverClient.features.eventBuffer || {};
    return dragData.assignmentRecords.some((assignmentRecord) => {
      const newResource = me.getNewResource(dragData, assignmentRecord.resource, indexDiff), eventRecord = assignmentRecord.event, eventStart = bufferIsUnavailableTime && eventRecord.outerStartDate || eventRecord.startDate, eventEnd = bufferIsUnavailableTime && eventRecord.outerEndDate || eventRecord.endDate, startDate2 = DateHelper.add(eventStart, msDiff, "ms"), endDate = DateHelper.add(eventEnd, msDiff, "ms");
      return !me.currentOverClient.isDateRangeAvailable(startDate2, endDate, dragData.assignmentRecords, newResource);
    });
  }
  //endregion
  //region Update records
  /**
   * Update events being dragged.
   * @private
   * @param context Drag data.
   */
  async updateRecords(context) {
    const me = this, fromScheduler = me.client, toScheduler = me.currentOverClient, copyKeyPressed = me.mode === "copy", { draggedEntities, timeDiff, initialAssignmentsState } = context, originalStartDate = initialAssignmentsState[0].startDate, droppedStartDate = me.adjustStartDate(originalStartDate, timeDiff);
    let result;
    if (!context.externalDropTarget) {
      if (!toScheduler.timeAxis.timeSpanInAxis(droppedStartDate, DateHelper.add(droppedStartDate, draggedEntities[0].event.rawDurationMS, "ms"))) {
        context.valid = false;
      }
      if (context.valid) {
        fromScheduler.eventStore.suspendAutoCommit();
        toScheduler.eventStore.suspendAutoCommit();
        result = await me.updateAssignments(fromScheduler, toScheduler, context, copyKeyPressed);
        fromScheduler.eventStore.resumeAutoCommit();
        toScheduler.eventStore.resumeAutoCommit();
      }
    }
    if (context.valid) {
      toScheduler.trigger("eventDrop", Object.assign(me.getTriggerParams(context), {
        isCopy: copyKeyPressed,
        copyMode: me.copyMode,
        domEvent: context.browserEvent,
        targetEventRecord: context.targetEventRecord,
        targetResourceRecord: context.newResource,
        externalDropTarget: context.externalDropTarget
      }));
    }
    return result;
  }
  /**
   * Update assignments being dragged
   * @private
   */
  async updateAssignments(fromScheduler, toScheduler, context, copy) {
    var _a5, _b2, _c2;
    const me = this, { copyMode } = me, isCrossScheduler = fromScheduler !== toScheduler, { isVertical } = toScheduler, {
      assignmentStore: fromAssignmentStore,
      eventStore: fromEventStore
    } = fromScheduler, {
      assignmentStore: toAssignmentStore,
      eventStore: toEventStore
    } = toScheduler, fromResourceStore = fromScheduler.isVertical ? fromScheduler.resourceStore : fromScheduler.store, {
      eventRecords,
      assignmentRecords,
      timeDiff,
      initialAssignmentsState,
      newResource: toResource
    } = context, { unifiedDrag } = me, useSingleAssignment = toEventStore.usesSingleAssignment || toEventStore.usesSingleAssignment !== false && fromEventStore.usesSingleAssignment, effectiveCopyMode = copyMode === "event" ? "event" : copyMode === "assignment" ? "assignment" : useSingleAssignment ? "event" : "assignment", event1Date = me.adjustStartDate(assignmentRecords[0].event.startDate, timeDiff), eventsToAdd = [], eventsToRemove = [], assignmentsToAdd = [], assignmentsToRemove = [], eventsToCheck = [], eventsToBatch = /* @__PURE__ */ new Set();
    fromScheduler.suspendRefresh();
    toScheduler.suspendRefresh();
    let updated = false, updatedEvent = false;
    const indexDiff = me.getIndexDiff(context);
    if (isVertical) {
      eventRecords.forEach((draggedEvent, i) => {
        const eventBar = context.eventBarEls[i];
        delete draggedEvent.instanceMeta(fromScheduler).hasTemporaryDragElement;
        if (eventBar.dataset.transient) {
          eventBar.remove();
        }
      });
    }
    const eventBarEls = context.eventBarEls.slice(), addedEvents = [], copiedAssignmentsMap = {};
    for (let i = 0; i < assignmentRecords.length; i++) {
      const originalAssignment = assignmentRecords[i];
      let draggedEvent = originalAssignment.event, draggedAssignment;
      if (copy) {
        draggedAssignment = originalAssignment.copy();
        copiedAssignmentsMap[originalAssignment.id] = draggedAssignment;
      } else {
        draggedAssignment = originalAssignment;
      }
      if (!draggedAssignment.isOccurrenceAssignment && (!fromAssignmentStore.includes(originalAssignment) || !fromEventStore.includes(draggedEvent))) {
        eventBarEls[i].remove();
        eventBarEls.splice(i, 1);
        assignmentRecords.splice(i, 1);
        i--;
        continue;
      }
      const initialState = initialAssignmentsState[i], originalEventRecord = draggedEvent, originalStartDate = initialState.startDate, originalResourceRecord = initialState.resource, newStartDate = this.constrainDragToTimeSlot ? originalStartDate : unifiedDrag ? event1Date : me.adjustStartDate(originalStartDate, timeDiff);
      if (!fromAssignmentStore.equals(toAssignmentStore)) {
        const keepEvent = originalEventRecord.assignments.length > 1 || copy;
        let newAssignment;
        if (copy) {
          newAssignment = draggedAssignment;
        } else {
          newAssignment = draggedAssignment.copy();
          copiedAssignmentsMap[draggedAssignment.id] = newAssignment;
        }
        if (newAssignment.event && !useSingleAssignment) {
          newAssignment.event = newAssignment.event.id;
          newAssignment.resource = newAssignment.resource.id;
        }
        if (!copy) {
          assignmentsToRemove.push(draggedAssignment);
        }
        if (!keepEvent) {
          eventsToRemove.push(originalEventRecord);
        }
        if (copy && (copyMode === "event" || copyMode === "auto" && toEventStore.usesSingleAssignment) || !toEventStore.getById(originalEventRecord.id)) {
          draggedEvent = toEventStore.createRecord({
            ...originalEventRecord.data,
            children: (_a5 = originalEventRecord.children) == null ? void 0 : _a5.map((child) => child.copy()),
            // If we're copying the event (not making new assignment to existing), we need to generate
            // phantom id to link event to the assignment record
            id: copy && (copyMode === "event" || copyMode === "auto") ? void 0 : originalEventRecord.id,
            // Engine gets mad if not nulled
            calendar: null
          });
          newAssignment.set({
            eventId: draggedEvent.id,
            event: draggedEvent
          });
          eventsToAdd.push(draggedEvent);
        }
        if (!useSingleAssignment) {
          assignmentsToAdd.push(newAssignment);
        }
        draggedAssignment = newAssignment;
      }
      let newResource = toResource, reassignedFrom = null;
      if (!unifiedDrag) {
        newResource = me.getNewResource(context, originalResourceRecord, indexDiff) || toResource;
      }
      newResource = newResource.$original;
      const isCrossResource = draggedAssignment.resourceId !== newResource.id;
      if (isCrossResource) {
        reassignedFrom = fromResourceStore.getById(draggedAssignment.resourceId);
        if (copy && fromAssignmentStore.equals(toAssignmentStore)) {
          draggedAssignment.setData({
            resource: null,
            resourceId: null
          });
          draggedAssignment.resource = newResource;
          draggedAssignment.event = toEventStore.getById(draggedAssignment.eventId);
          const shouldCopyEvent = copyMode === "event" || fromEventStore.usesSingleAssignment && copyMode === "auto";
          if (shouldCopyEvent) {
            draggedEvent = draggedEvent.copy();
            draggedEvent.meta.endDateCached = me.adjustStartDate(draggedEvent.endDate, timeDiff);
            draggedEvent.endDate = null;
            draggedAssignment.event = draggedEvent;
            if (toEventStore.usesSingleAssignment) {
              draggedEvent.resource = newResource;
              draggedEvent.resourceId = newResource.id;
            }
          }
          if (!toAssignmentStore.find((a) => a.eventId === draggedAssignment.eventId && a.resourceId === draggedAssignment.resourceId) && !assignmentsToAdd.find((r) => r.eventId === draggedAssignment.eventId && r.resourceId === draggedAssignment.resourceId)) {
            shouldCopyEvent && eventsToAdd.push(draggedEvent);
            assignmentsToAdd.push(draggedAssignment);
          }
        } else {
          draggedAssignment.resource = newResource;
          if (useSingleAssignment) {
            draggedAssignment.event.resourceId = newResource.id;
          } else {
            draggedAssignment.resourceId = newResource.id;
          }
        }
        draggedEvent.isEvent && eventsToBatch.add(draggedEvent);
        updated = true;
        if (draggedEvent.isOccurrence) {
          draggedEvent.set("newResource", newResource);
        }
        if (isCrossScheduler && useSingleAssignment) {
          draggedEvent.resourceId = newResource.id;
        }
      } else {
        if (copy && (copyMode === "event" || copyMode === "auto" && fromEventStore.usesSingleAssignment) && !eventsToAdd.includes(draggedEvent)) {
          draggedEvent = draggedEvent.copy();
          draggedEvent.meta.endDateCached = me.adjustStartDate(draggedEvent.endDate, timeDiff);
          draggedEvent.endDate = null;
          eventsToAdd.push(draggedEvent);
          draggedAssignment.event = draggedEvent;
          if (toEventStore.usesSingleAssignment) {
            draggedEvent.set({
              resource: newResource,
              resourceId: newResource.id
            });
          }
          assignmentsToAdd.push(draggedAssignment);
        }
      }
      if (!eventsToCheck.find((ev) => ev.draggedEvent === draggedEvent) && !DateHelper.isEqual(draggedEvent.startDate, newStartDate)) {
        while (!draggedEvent.isOccurrence && draggedEvent.isBatchUpdating) {
          draggedEvent.endBatch(true);
        }
        const shouldKeepStartDate = copy && !isCrossScheduler && !useSingleAssignment && effectiveCopyMode === "assignment" && isCrossResource;
        if (!shouldKeepStartDate) {
          draggedEvent.startDate = newStartDate;
          eventsToCheck.push({ draggedEvent, originalStartDate });
        }
        draggedEvent.isEvent && eventsToBatch.add(draggedEvent);
        updatedEvent = true;
      }
      toScheduler.processEventDrop({
        eventRecord: draggedEvent,
        resourceRecord: newResource,
        element: i === 0 ? context.context.element : context.context.relatedElements[i - 1],
        context,
        toScheduler,
        reassignedFrom,
        eventsToAdd,
        addedEvents,
        draggedAssignment
      });
      toScheduler.trigger("processEventDrop", {
        originalAssignment,
        draggedAssignment,
        context,
        copyMode,
        isCopy: copy
      });
    }
    fromAssignmentStore.remove(assignmentsToRemove);
    fromEventStore.remove(eventsToRemove);
    toAssignmentStore.add(assignmentsToAdd);
    if (copy && fromAssignmentStore.equals(toAssignmentStore)) {
      const { syncIdMap } = fromScheduler.foregroundCanvas;
      Object.entries(copiedAssignmentsMap).forEach(([originalId, cloneRecord]) => {
        const element = syncIdMap[originalId];
        delete syncIdMap[originalId];
        syncIdMap[cloneRecord.id] = element;
      });
    }
    eventsToAdd.length && addedEvents.push(...toEventStore.add(eventsToAdd));
    addedEvents == null ? void 0 : addedEvents.forEach((added) => eventsToBatch.add(added));
    if (assignmentsToRemove.length || eventsToRemove.length || assignmentsToAdd.length || eventsToAdd.length) {
      updated = true;
    }
    if (updated || updatedEvent) {
      if (!me.constrainDragToTimeline) {
        for (let i = 0; i < assignmentRecords.length; i++) {
          const assignmentRecord = copiedAssignmentsMap[assignmentRecords[i].id] || assignmentRecords[i], originalDraggedEvent = assignmentRecord.event, draggedEvent = (addedEvents == null ? void 0 : addedEvents.find((r) => r.id === originalDraggedEvent.id)) || originalDraggedEvent, eventBar = context.eventBarEls[i], element = i === 0 ? context.context.element : context.context.relatedElements[i - 1], inTimeAxis = toScheduler.isInTimeAxis(draggedEvent);
          delete draggedEvent.meta.endDateCached;
          if (!copy) {
            DomSync.removeChild(eventBar.parentElement, eventBar);
          }
          if (draggedEvent.resource && (isVertical || toScheduler.rowManager.getRowFor(assignmentRecord.resource)) && inTimeAxis) {
            if (!draggedEvent.parent || draggedEvent.parent.isRoot) {
              const elRect = Rectangle.from(element, toScheduler.foregroundCanvas, true), x = toScheduler.rtl ? toScheduler.timeAxisViewModel.totalSize - elRect.right : elRect.x;
              DomHelper.setTopInsetInlineStart(element, elRect.y, x);
              DomSync.addChild(toScheduler.foregroundCanvas, element, draggedEvent.assignments[0].id);
              isCrossScheduler && toScheduler.processCrossSchedulerEventDrop({
                eventRecord: draggedEvent,
                toScheduler
              });
            }
            element.classList.remove("b-sch-event-hover", "b-active", "b-drag-proxy", "b-dragging");
            element.retainElement = false;
          } else {
            element.remove();
          }
        }
      }
      useSingleAssignment && eventsToBatch.forEach((eventRecord) => eventRecord.beginBatch());
      const toProject = (_b2 = toEventStore.$master.project) != null ? _b2 : toScheduler.project, fromProject = (_c2 = fromEventStore.$master.project) != null ? _c2 : fromScheduler.project;
      await Promise.all([
        toProject !== fromProject ? toProject.commitAsync() : null,
        fromProject.commitAsync()
      ]);
      useSingleAssignment && eventsToBatch.forEach((eventRecord) => eventRecord.endBatch(false, true));
    }
    if (!updated) {
      updated = eventsToCheck.some(
        ({ draggedEvent, originalStartDate }) => !DateHelper.isEqual(draggedEvent.startDate, originalStartDate)
      );
    }
    toScheduler.resumeRefresh(false);
    fromScheduler.resumeRefresh(false);
    if (assignmentRecords.length > 0) {
      if (!updated) {
        context.valid = false;
      } else {
        eventBarEls.forEach((el) => delete el.lastDomConfig);
        toScheduler.refreshWithTransition();
        if (isCrossScheduler) {
          fromScheduler.refreshWithTransition();
          toScheduler.selectedEvents = addedEvents;
        }
      }
    }
  }
  //endregion
  //region Drag data
  getProductDragContext(dragData) {
    const me = this, { currentOverClient: scheduler } = me, target = dragData.browserEvent.target, previousResolvedResource = dragData.newResource || dragData.resourceRecord, previousTargetEventRecord = dragData.targetEventRecord;
    let targetEventRecord = scheduler ? me.resolveEventRecord(target, scheduler) : null, newResource, externalDropTarget;
    if (dragData.eventRecords.includes(targetEventRecord)) {
      targetEventRecord = null;
    }
    if (me.constrainDragToResource) {
      newResource = dragData.resourceRecord;
    } else if (!me.constrainDragToTimeline) {
      newResource = me.resolveResource();
    } else if (scheduler) {
      newResource = me.resolveResource() || dragData.newResource || dragData.resourceRecord;
    }
    if (targetEventRecord && !targetEventRecord.isParent && !targetEventRecord.parent) {
      const targetEventResourceId = target.parentElement.dataset.resourceId;
      if (newResource && targetEventResourceId != null && targetEventResourceId != newResource.id) {
        const eventsForActualResource = scheduler.eventStore.getEvents({
          startDate: dragData.startDate,
          endDate: dragData.endDate,
          resourceRecord: newResource
        });
        targetEventRecord = eventsForActualResource[0];
      }
    }
    const { assignmentRecords, eventRecords } = dragData, isOverNewResource = previousResolvedResource !== newResource;
    let valid = Boolean(newResource && !newResource.isSpecialRow);
    if (!newResource && me.externalDropTargetSelector) {
      externalDropTarget = target.closest(me.externalDropTargetSelector);
      valid = Boolean(externalDropTarget);
    }
    return {
      valid,
      externalDropTarget,
      eventRecords,
      assignmentRecords,
      newResource,
      targetEventRecord,
      dirty: isOverNewResource || targetEventRecord !== previousTargetEventRecord,
      proxyElements: [dragData.context.element, ...dragData.context.relatedElements || []]
    };
  }
  getMinimalDragData(info) {
    const me = this, { scheduler } = me, { resourceStore } = scheduler, element = me.getElementFromContext(info), eventRecord = me.resolveEventRecord(element, scheduler), resourceRecord = scheduler.resolveResourceRecord(element), assignmentRecord = scheduler.resolveAssignmentRecord(element);
    let assignmentRecords = assignmentRecord ? [assignmentRecord] : [];
    if (assignmentRecord && (scheduler.isAssignmentSelected(assignmentRecords[0]) || me.drag.startEvent.ctrlKey && scheduler.multiEventSelect)) {
      assignmentRecords.push.apply(assignmentRecords, me.getRelatedRecords(assignmentRecord));
    }
    assignmentRecords = assignmentRecords.filter(
      ({ resource }) => resourceStore.isAvailable(resource) || (resource == null ? void 0 : resource.$links.some((l) => resourceStore.isAvailable(l)))
    );
    const eventRecords = [...new Set(assignmentRecords.map((assignment) => assignment.event))];
    return {
      eventRecord,
      resourceRecord,
      assignmentRecord,
      eventRecords,
      assignmentRecords
    };
  }
  setupProductDragData(info) {
    var _a5;
    const me = this, { scheduler } = me, element = me.getElementFromContext(info), {
      eventRecord,
      resourceRecord,
      assignmentRecord,
      assignmentRecords
    } = me.getMinimalDragData(info), eventBarEls = [];
    if (me.constrainDragToResource && !resourceRecord) {
      throw new Error("Resource could not be resolved for event: " + eventRecord.id);
    }
    let dateConstraints;
    if (me.constrainDragToTimeline) {
      dateConstraints = (_a5 = me.getDateConstraints) == null ? void 0 : _a5.call(me, resourceRecord, eventRecord);
      const constrainRectangle = me.constrainRectangle = me.getConstrainingRectangle(dateConstraints, resourceRecord, eventRecord), eventRegion = Rectangle.from(element, scheduler.timeAxisSubGridElement), isDateConstrained = Boolean(dateConstraints.start);
      super.setupConstraints(
        constrainRectangle,
        eventRegion,
        scheduler.timeAxisViewModel.snapPixelAmount,
        isDateConstrained && scheduler.isHorizontal,
        isDateConstrained && scheduler.isVertical
      );
    }
    assignmentRecords.forEach((assignment) => {
      let eventBarEl = scheduler.getElementFromAssignmentRecord(assignment, true, false, scheduler.isLockedRowsView);
      if (!eventBarEl) {
        eventBarEl = scheduler.currentOrientation.addTemporaryDragElement(assignment.event, assignment.resource);
      }
      eventBarEls.push(eventBarEl);
    });
    return {
      record: assignmentRecord,
      draggedEntities: assignmentRecords,
      dateConstraints: (dateConstraints == null ? void 0 : dateConstraints.start) ? dateConstraints : null,
      // Create copies of the elements
      eventBarCopies: eventBarEls.map((el) => me.createProxy(el)),
      eventBarEls
    };
  }
  getDateConstraints(resourceRecord, eventRecord) {
    var _a5;
    const { scheduler } = this, externalDateConstraints = (_a5 = scheduler.getDateConstraints) == null ? void 0 : _a5.call(scheduler, resourceRecord, eventRecord);
    let minDate, maxDate;
    if (this.constrainDragToTimeSlot) {
      minDate = eventRecord.startDate;
      maxDate = eventRecord.endDate;
    } else if (externalDateConstraints) {
      minDate = externalDateConstraints.start || /* @__PURE__ */ new Date(0);
      maxDate = externalDateConstraints.end || new Date(9999, 0, 1);
    }
    return {
      start: minDate,
      end: maxDate
    };
  }
  getConstrainingRectangle(dateRange, resourceRecord, eventRecord) {
    return this.scheduler.getScheduleRegion(this.constrainDragToResource && resourceRecord, eventRecord, true, dateRange && {
      start: dateRange.start,
      end: dateRange.end
    });
  }
  /**
   * Initializes drag data (dates, constraints, dragged events etc). Called when drag starts.
   * @private
   * @param info
   * @returns {*}
   */
  getDragData(info) {
    const dragData = this.getMinimalDragData(info) || {};
    return {
      ...super.getDragData(info),
      ...dragData,
      initialAssignmentsState: dragData.assignmentRecords.map((assignment) => ({
        startDate: assignment.event.startDate,
        resource: assignment.resource,
        assignment
      }))
    };
  }
  /**
   * Provide your custom implementation of this to allow additional selected records to be dragged together with the original one.
   * @param {Scheduler.model.AssignmentModel} assignmentRecord The assignment about to be dragged
   * @returns {Scheduler.model.AssignmentModel[]} An array of assignment records to drag together with the original
   */
  getRelatedRecords(assignmentRecord) {
    return this.scheduler.selectedAssignments.filter((selectedRecord) => selectedRecord !== assignmentRecord && !selectedRecord.resource.readOnly && selectedRecord.event.isDraggable);
  }
  /**
   * Get correct axis coordinate depending on schedulers mode (horizontal -> x, vertical -> y). Also takes milestone
   * layout into account.
   * @private
   * @param {Scheduler.model.EventModel} eventRecord Record being dragged
   * @param {HTMLElement} element Element being dragged
   * @param {Number[]} coord XY coordinates
   * @returns {Number|Number[]} X,Y or XY
   */
  getCoordinate(eventRecord, element, coord) {
    const scheduler = this.currentOverClient;
    if (scheduler.isHorizontal) {
      let x = coord[0];
      if (scheduler.milestoneLayoutMode !== "default" && eventRecord.isMilestone) {
        switch (scheduler.milestoneAlign) {
          case "center":
            x += element.offsetWidth / 2;
            break;
          case "end":
            x += element.offsetWidth;
            break;
        }
      }
      return x;
    } else {
      let y = coord[1];
      if (scheduler.milestoneLayoutMode !== "default" && eventRecord.isMilestone) {
        switch (scheduler.milestoneAlign) {
          case "center":
            y += element.offsetHeight / 2;
            break;
          case "end":
            y += element.offsetHeight;
            break;
        }
      }
      return y;
    }
  }
  /**
   * Get resource record occluded by the drag proxy.
   * @private
   * @returns {Scheduler.model.ResourceModel}
   */
  resolveResource() {
    const me = this, client = me.currentOverClient, { isHorizontal } = client, {
      context,
      browserEvent,
      dragProxy
    } = me.dragData, element = dragProxy || context.element, pageRect = Rectangle.from(element, null, true), y = client.isVertical || me.unifiedDrag ? context.clientY : pageRect.center.y, localRect = Rectangle.from(element, client.timeAxisSubGridElement, true), { x: lx, y: ly } = localRect.center;
    let resource = null, isInsideClientEl;
    if (browserEvent.touches) {
      isInsideClientEl = Rectangle.from(client.element).contains(Point.from(browserEvent, true));
    } else {
      isInsideClientEl = client.element.contains(me.getMouseMoveEventTarget(browserEvent));
    }
    if (isInsideClientEl) {
      if (isHorizontal) {
        const row = client.rowManager.getRowAt(y);
        resource = row && client.store.storage.getAt(row.dataIndex);
      } else {
        resource = client.resolveResourceRecord(client.timeAxisSubGridElement.querySelector(".b-sch-timeaxis-cell"), [lx, ly]);
      }
    }
    return resource == null ? void 0 : resource.$original;
  }
  //endregion
  //region Other stuff
  getRecordElement(assignmentRecord) {
    return this.client.getElementFromAssignmentRecord(assignmentRecord, true);
  }
  // Used by the Dependencies feature to draw lines to the drag proxy instead of the original event element
  getProxyElement(assignmentRecord) {
    var _a5;
    const { dragData } = this;
    if (this.isDragging && ((_a5 = dragData == null ? void 0 : dragData.proxyElements) == null ? void 0 : _a5.length)) {
      const index = dragData.assignmentRecords.indexOf(assignmentRecord);
      if (index >= 0) {
        return dragData.proxyElements[index];
      }
    }
    return null;
  }
  //endregion
  //#region Salesforce hooks
  getMouseMoveEventTarget(event) {
    return event.target;
  }
  //#endregion
};
//region Config
__publicField(EventDrag, "$name", "EventDrag");
EventDrag._$name = "EventDrag";
GridFeatureManager.registerFeature(EventDrag, true, "Scheduler");
GridFeatureManager.registerFeature(EventDrag, false, "ResourceHistogram");

// ../Scheduler/lib/Scheduler/feature/EventDragCreate.js
var EventDragCreate = class extends DragCreateBase {
  //endregion
  //region Events
  /**
   * Fires on the owning Scheduler after the new event has been created.
   * @event dragCreateEnd
   * @on-owner
   * @param {Scheduler.view.Scheduler} source
   * @param {Scheduler.model.EventModel} eventRecord The new `EventModel` record.
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource for the row in which the event is being
   * created.
   * @param {MouseEvent} event The ending mouseup event.
   * @param {HTMLElement} eventElement The DOM element representing the newly created event un the UI.
   */
  /**
   * Fires on the owning Scheduler at the beginning of the drag gesture. Returning `false` from a listener prevents
   * the drag create operation from starting.
   *
   * ```javascript
   * const scheduler = new Scheduler({
   *     listeners : {
   *         beforeDragCreate({ date }) {
   *             // Prevent drag creating events in the past
   *             return date >= Date.now();
   *         }
   *     }
   * });
   * ```
   *
   * @event beforeDragCreate
   * @on-owner
   * @preventable
   * @param {Scheduler.view.Scheduler} source
   * @param {Scheduler.model.ResourceModel} resourceRecord
   * @param {Date} date The datetime associated with the drag start point.
   */
  /**
   * Fires on the owning Scheduler after the drag start has created a new Event record.
   * @event dragCreateStart
   * @on-owner
   * @param {Scheduler.view.Scheduler} source
   * @param {Scheduler.model.EventModel} eventRecord The event record being created
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource record
   * @param {HTMLElement} eventElement The element representing the new event.
   */
  /**
   * Fires on the owning Scheduler to allow implementer to prevent immediate finalization by setting
   * `data.context.async = true` in the listener, to show a confirmation popup etc.
   *
   * ```javascript
   *  scheduler.on('beforeDragCreateFinalize', ({context}) => {
   *      context.async = true;
   *      setTimeout(() => {
   *          // async code don't forget to call finalize
   *          context.finalize();
   *      }, 1000);
   *  })
   * ```
   *
   * Note that at this point the new `eventRecord` does not yet have the dates set, you can instead find the dates in
   * the context object.
   *
   * @event beforeDragCreateFinalize
   * @on-owner
   * @param {Scheduler.view.Scheduler} source Scheduler instance
   * @param {Scheduler.model.EventModel} eventRecord The event record being created
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource record
   * @param {HTMLElement} eventElement The element representing the new Event record
   * @param {Object} context
   * @param {Date} context.startDate The start date of the event being created
   * @param {Date} context.endDate The end date of the event being created
   * @param {Boolean} context.async Set true to handle drag create asynchronously (e.g. to wait for user
   * confirmation)
   * @param {Function} context.finalize Call this method to finalize drag create. This method accepts one
   * argument: pass true to update records, or false, to ignore changes
   */
  /**
   * Fires on the owning Scheduler at the end of the drag create gesture whether or not
   * a new event was created by the gesture.
   * @event afterDragCreate
   * @on-owner
   * @param {Scheduler.view.Scheduler} source
   * @param {Scheduler.model.EventModel} eventRecord The event record being created
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource record
   * @param {HTMLElement} eventElement The element representing the created event record
   */
  //endregion
  //region Init
  get scheduler() {
    return this.client;
  }
  get store() {
    return this.client.eventStore;
  }
  get project() {
    return this.client.project;
  }
  updateLockLayout(lock) {
    this.dragActiveCls = `b-dragcreating${lock ? " b-dragcreate-lock" : ""}`;
  }
  //endregion
  //region Scheduler specific implementation
  handleBeforeDragCreate(drag, eventRecord, event) {
    var _a5;
    const me = this, { scheduler } = me, {
      // provided if user is dragging over a model link
      resourceRecordLink,
      // actual resource model
      resourceRecord
    } = drag;
    if (me.disabled || // - resource is read only
    resourceRecord.readOnly || // - resource record is not visible (collapsed or filtered out?)
    !(scheduler.resourceStore.isAvailable(resourceRecord) || // if that's a model link then check "store" (could be a view store made by TreeGroup)
    resourceRecordLink && scheduler.store.isAvailable(resourceRecordLink)) || // - date range is not allocated for the resource
    !scheduler.isDateRangeAvailable(drag.mousedownDate, drag.mousedownDate, null, resourceRecord)) {
      return false;
    }
    const isWorkingTime2 = !scheduler.isSchedulerPro || eventRecord.ignoreResourceCalendar || resourceRecord.isWorkingTime(drag.mousedownDate), result = isWorkingTime2 && scheduler.trigger("beforeDragCreate", {
      resourceRecord,
      date: drag.mousedownDate,
      event
    });
    me.dateConstraints = (_a5 = scheduler.getDateConstraints) == null ? void 0 : _a5.call(scheduler, resourceRecord, eventRecord);
    return result;
  }
  dragStart(drag) {
    var _a5, _b2, _c2;
    const me = this, { client } = me;
    if ((_b2 = (_a5 = client.project) == null ? void 0 : _a5.stm) == null ? void 0 : _b2.isNavigatingRevisions) {
      client.trigger("eventDragCreatePrevented", { reason: "revision" });
      return false;
    }
    const {
      eventStore,
      assignmentStore,
      enableEventAnimations,
      enableTransactionalFeatures
    } = client, {
      // provided if user is dragging over a model link
      resourceRecordLink,
      // actual resource model
      resourceRecord
    } = drag, eventRecord = me.createEventRecord(drag), resourceRecords = [resourceRecord];
    eventRecord.set("duration", DateHelper.diff(eventRecord.startDate, eventRecord.endDate, eventRecord.durationUnit, true));
    eventRecord.isCreating = true;
    eventRecord.meta.isDragCreating = true;
    client.features.taskEdit && client.features.taskEdit.doCancel();
    if (me.handleBeforeDragCreate(drag, eventRecord, drag.event) === false) {
      return false;
    }
    me.captureStm(true);
    let assignmentRecords = [];
    if (resourceRecord) {
      if (eventStore.usesSingleAssignment || !enableTransactionalFeatures) {
        assignmentRecords = assignmentStore.assignEventToResource(eventRecord, resourceRecord);
      } else {
        assignmentRecords = [assignmentStore.createRecord({
          event: eventRecord,
          resource: resourceRecord
        })];
      }
    }
    if (client.trigger("beforeEventAdd", { eventRecord, resourceRecords, assignmentRecords }) === false) {
      if (eventStore.usesSingleAssignment || !enableTransactionalFeatures) {
        assignmentStore.remove(assignmentRecords);
      }
      return false;
    }
    if (me.lockLayout) {
      eventRecord.meta.excludeFromLayout = true;
    }
    (_c2 = client.onEventCreated) == null ? void 0 : _c2.call(client, eventRecord);
    client.enableEventAnimations = false;
    const result = eventStore.add(eventRecord);
    this.project.commitAsync().then(() => client.enableEventAnimations = enableEventAnimations);
    if (!result) {
      if (eventStore.usesSingleAssignment || !enableTransactionalFeatures) {
        assignmentStore.remove(assignmentRecords);
      }
      return false;
    }
    if (!eventStore.usesSingleAssignment && enableTransactionalFeatures) {
      assignmentStore.add(assignmentRecords[0]);
    }
    client.isCreating = true;
    if (client.isHorizontal) {
      client.rowManager.renderRows([client.getRowFor(resourceRecordLink || resourceRecord)]);
    } else {
      client.refreshRows();
    }
    client.isCreating = false;
    drag.itemElement = drag.element = client.getElementFromEventRecord(eventRecord);
    if (!DomHelper.isInView(drag.itemElement)) {
      client.scrollable.scrollIntoView(drag.itemElement, {
        animate: true,
        edgeOffset: client.barMargin
      });
    }
    return super.dragStart(drag);
  }
  checkValidity(context, event) {
    const me = this, { client } = me, resourceRecord = context.resourceRecord = me.dragging.resourceRecord;
    if (!client.isDateRangeAvailable(context.startDate, context.endDate, context.eventRecord, resourceRecord)) {
      return {
        valid: false,
        message: me.L("L{EventDrag.eventOverlapsExisting}")
      };
    }
    return me.createValidatorFn.call(me.validatorFnThisObj || me, context, event);
  }
  // Determine if resource already has events or not
  isRowEmpty(resourceRecord) {
    const events = this.store.getEventsForResource(resourceRecord);
    return !events || !events.length;
  }
  //endregion
  /**
   * Creates an event by the event object coordinates
   * @param {Object} drag The Bryntum event object
   * @private
   */
  createEventRecord(drag) {
    const me = this, { client } = me, dimension = client.isHorizontal ? "X" : "Y", {
      timeAxis,
      eventStore,
      weekStartDay
    } = client, {
      event,
      mousedownDate
    } = drag, draggingEnd = me.draggingEnd = event[`page${dimension}`] > drag.startEvent[`page${dimension}`], eventConfig = {
      name: eventStore.modelClass.fieldMap.name.defaultValue || me.L("L{Object.newEvent}"),
      startDate: draggingEnd ? DateHelper.floor(mousedownDate, timeAxis.resolution, null, weekStartDay) : mousedownDate,
      endDate: draggingEnd ? mousedownDate : DateHelper.ceil(mousedownDate, timeAxis.resolution, null, weekStartDay)
    };
    if (client.project.isGanttProjectMixin) {
      ObjectHelper.assign(eventConfig, {
        constraintDate: eventConfig.startDate,
        constraintType: "startnoearlierthan"
      });
    }
    return eventStore.createRecord(eventConfig);
  }
  async internalUpdateRecord(context, eventRecord) {
    await super.internalUpdateRecord(context, eventRecord);
    if (!this.client.hasEventEditor) {
      context.eventRecord.isCreating = false;
    }
  }
  async finalizeDragCreate(context) {
    const { meta } = context.eventRecord;
    meta.excludeFromLayout = false;
    meta.isDragCreating = false;
    const transferred = await super.finalizeDragCreate(context);
    if (!transferred) {
      await this.freeStm(true);
    } else {
      this.hasStmCapture = false;
    }
    return transferred;
  }
  async cancelDragCreate(context) {
    var _a5;
    await super.cancelDragCreate(context);
    await this.freeStm(false);
    await ((_a5 = this.afterCancel) == null ? void 0 : _a5.call(this));
  }
  getTipHtml(...args) {
    const html = super.getTipHtml(...args), { element } = this.tip;
    element.classList.add("b-sch-dragcreate-tooltip");
    element.classList.toggle("b-too-narrow", this.dragging.context.tooNarrow);
    return html;
  }
  onAborted(context) {
    var _a5, _b2;
    const { eventRecord, resourceRecord } = context.eventContexts[0];
    (_b2 = (_a5 = this.store).unassignEventFromResource) == null ? void 0 : _b2.call(_a5, eventRecord, resourceRecord);
    this.store.remove(eventRecord);
  }
};
//region Config
__publicField(EventDragCreate, "$name", "EventDragCreate");
__publicField(EventDragCreate, "configurable", {
  /**
   * Locks the layout during drag create, overriding the default behaviour that uses the same rendering
   * pathway for drag creation as for already existing events.
   *
   * This more closely resembles the behaviour of versions prior to 4.2.0.
   *
   * Enabling this config also leads to cheaper drag creation, only the events of the affected resource are
   * refreshed during the operation.
   *
   * For even cheaper drag creation, configure it as `'minimal-updates'`. In this mode, no other events are
   * updated during the operation.
   *
   * @config {Boolean|'minimal-updates'} lockLayout
   * @default true
   */
  /**
   * An empty function by default, but provided so that you can perform custom validation on the event being
   * created. Return `true` if the new event is valid, `false` to prevent an event being created.
   * @param {Object} context A drag create context
   * @param {Date} context.startDate Event start date
   * @param {Date} context.endDate Event end date
   * @param {Scheduler.model.EventModel} context.record Event record
   * @param {Scheduler.model.ResourceModel} context.resourceRecord Resource record
   * @param {Event} event The event object
   * @returns {Boolean} `true` if this validation passes
   * @config {Function}
   */
  validatorFn: () => true
});
EventDragCreate._$name = "EventDragCreate";
GridFeatureManager.registerFeature(EventDragCreate, true, "Scheduler");
GridFeatureManager.registerFeature(EventDragCreate, false, "ResourceHistogram");

// ../Scheduler/lib/Scheduler/feature/EventDragSelect.js
var EventDragSelect = class extends Delayable_default(InstancePlugin) {
  constructor() {
    super(...arguments);
    __publicField(this, "targetSelector", ".b-sch-timeaxis-cell, .b-timeaxissubgrid");
  }
  construct(client, config) {
    if (!client.multiEventSelect) {
      client.multiEventSelect = true;
    }
    super.construct(client, config);
  }
  //endregion
  //region Plugin config
  // Plugin configuration. This plugin chains some of the functions in Scheduler.
  static get pluginConfig() {
    return {
      chain: ["onElementMouseDown", "onElementMouseMove"]
    };
  }
  //endregion
  onElementMouseDown(event) {
    const me = this, { client } = me, { foregroundCanvas } = client, { target, button, touches, clientX, clientY } = event, canvasRect = Rectangle.from(foregroundCanvas, true);
    if (touches || button !== 0 || me.disabled || !target.matches(me.targetSelector) || client.trigger("beforeEventDragSelect", { event }) === false) {
      return;
    }
    client.preventDragSelect = true;
    me.startX = clientX - canvasRect.x;
    me.startY = clientY - canvasRect.y;
    if (!me.element) {
      me.element = DomHelper.createElement({
        className: "b-dragselect-rect",
        retainElement: true
      });
    }
    client.foregroundCanvas.appendChild(me.element);
    me.element.style.transform = `translate(${me.startX}px, ${me.startY}px)`;
    client.navigator.disabled = true;
    me.mouseUpDetacher = EventHelper.on({
      element: document,
      mouseup: "onDocumentMouseUp",
      thisObj: me,
      capture: true
    });
  }
  get eventRectangles() {
    const { client } = this, selector = this.includeNested ? client.eventSelector : `${client.eventSelector}:not(.b-nested-event)`, eventElements = client.foregroundCanvas.querySelectorAll(selector);
    return Array.from(eventElements).map((el) => {
      const record = client.resolveAssignmentRecord(el);
      return {
        rectangle: Rectangle.from(el, true),
        record,
        selected: client.selectedAssignments.includes(record)
      };
    });
  }
  onElementMouseMove(event) {
    const me = this, { client } = me;
    if (typeof me.startX === "number") {
      const canvasRect = me.rectangle = Rectangle.from(me.client.foregroundCanvas, true), x = Math.min(Math.max(event.clientX - canvasRect.x, 0), canvasRect.width + 1), y = Math.min(Math.max(event.clientY - canvasRect.y, 0), canvasRect.height + 1), rect = new Rectangle(Math.min(x, me.startX), Math.min(y, me.startY), Math.abs(x - me.startX), Math.abs(y - me.startY));
      DomHelper.setTranslateXY(me.element, rect.x, rect.y);
      me.element.style.width = `${rect.width}px`;
      me.element.style.height = `${rect.height}px`;
      if (!me.dragging) {
        client.element.classList.add("b-dragselecting");
        if (!event.ctrlKey && !event.metaKey) {
          client.clearEventSelection();
        }
        me.originalSelection = client.selectedAssignments.slice();
        me.subGridElementRect = Rectangle.from(client.timeAxisSubGridElement, true);
        client.navigator.disabled = true;
        client.enableScrollingCloseToEdges(client.timeAxisSubGrid);
        me.dragging = true;
      }
      me.updateSelection();
    }
  }
  onDocumentMouseUp(event) {
    var _a5;
    const me = this, { client } = me, { selectedAssignments, navigator: navigator2 } = client, timeAxisRect = Rectangle.from(client.timeAxisSubGridElement);
    client.disableScrollingCloseToEdges(client.timeAxisSubGrid);
    if (!me.dragging && client.deselectAllOnScheduleClick) {
      client.clearEventSelection();
    }
    if (me.element) {
      me.element.style = "";
      me.element.remove();
    }
    client.element.classList.remove("b-dragselecting");
    me.startX = me.startY = null;
    navigator2.skipNextClick = timeAxisRect.contains(Point.from(event, true));
    navigator2.disabled = false;
    me.dragging = false;
    if (selectedAssignments.length) {
      navigator2.skipScrollIntoView = true;
      client.activeAssignment = selectedAssignments[selectedAssignments.length - 1];
      (_a5 = navigator2.activeItem) == null ? void 0 : _a5.focus();
      navigator2.skipScrollIntoView = false;
    }
    me.mouseUpDetacher();
    client.trigger("afterEventDragSelect");
  }
  updateSelection() {
    const me = this, selectionRect = me.rectangle = Rectangle.from(me.element, true), {
      eventRectangles,
      client
    } = me, selection = client.selectedAssignments.reduce((r, a) => {
      if (!client.getElementFromAssignmentRecord(a)) {
        r.push(a);
      }
      return r;
    }, []);
    for (const assignmentData of eventRectangles) {
      if (selectionRect.intersect(assignmentData.rectangle, true, true) && client.isEventSelectable(assignmentData.record.event) !== false) {
        assignmentData.selected = true;
        selection.push(assignmentData.record);
      }
    }
    selection.push(...me.originalSelection);
    client.selectedAssignments = selection;
    const dates = client.getStartEndDatesFromRectangle(selectionRect, void 0, void 0, void 0, false);
    client.trigger("eventDragSelect", {
      startDate: dates.start,
      endDate: dates.end,
      rectangle: selectionRect,
      selectedAssignments: client.selectedAssignments,
      selectedEvents: client.selectedEvents
    });
  }
};
// region Events
/**
 * Fires on the owning Scheduler before drag selection starts. Return false to prevent the operation.
 * @event beforeEventDragSelect
 * @preventable
 * @on-owner
 * @param {Scheduler.view.SchedulerBase} source The Scheduler instance
 * @param {Event} event The native browser DOM event
 */
//endregion
// region Init
__publicField(EventDragSelect, "$name", "EventDragSelect");
__publicField(EventDragSelect, "configurable", {
  /**
   * Configure as `true` to include nested events in the selection. Only applies to SchedulerPro, when using
   * nested events. When `false` only parent events and events without nesting will be selected.
   *
   * Marque selection cannot be started inside a parent event, and marque selecting a nested event will always
   * also select the parent event.
   *
   * ```javascript
   * const scheduler = new SchedulerPro({
   *     features : {
   *         nestedEvents : true, // Enable nested events
   *         eventDragSelect : {
   *             includeNested : true // Include nested events in the selection
   *         }
   *     }
   * });
   * ```
   *
   * @prp {Boolean}
   * @default false
   */
  includeNested: null
});
EventDragSelect._$name = "EventDragSelect";
GridFeatureManager.registerFeature(EventDragSelect, false, "Scheduler");

// ../Scheduler/lib/Scheduler/view/EventEditor.js
var EventEditor = class extends Popup {
  revertFocus() {
    var _a5, _b2, _c2;
    const { owner } = this, relatedTarget = (_a5 = this.focusInEvent) == null ? void 0 : _a5.relatedTarget;
    if (relatedTarget && !DomHelper.isFocusable(relatedTarget) && owner.isTimelineBase) {
      (_c2 = (_b2 = owner.focusedCell) == null ? void 0 : _b2.cell) == null ? void 0 : _c2.focus();
    }
    super.revertFocus();
  }
  updateLocalization() {
    super.updateLocalization(...arguments);
    this.initialTitle = this.title || "";
  }
  chainResourceStore() {
    return this.eventEditFeature.resourceStore.chain(
      (record) => !record.isSpecialRow,
      null,
      {
        // It doesn't need to be a Project-based Store
        storeClass: Store,
        // Need to show all records in the combo. Required in case resource store is a tree.
        excludeCollapsedRecords: false
      }
    );
  }
  processWidgetConfig(widget) {
    var _a5;
    if (((_a5 = widget.type) == null ? void 0 : _a5.includes("date")) && widget.weekStartDay == null) {
      widget.weekStartDay = this.weekStartDay;
    }
    if (widget.type === "extraItems") {
      return false;
    }
    const { eventEditFeature } = this, fieldConfig = {};
    if (widget.ref === "resourceField") {
      const { store } = widget;
      widget.store = this.chainResourceStore();
      if (store) {
        widget.store.setConfig(store);
      }
      if (!("multiSelect" in widget)) {
        widget.multiSelect = !eventEditFeature.eventStore.usesSingleAssignment;
      }
    }
    if ((widget.name === "startDate" || widget.name === "endDate") && widget.type === "date") {
      fieldConfig.format = eventEditFeature.dateFormat;
    }
    if ((widget.name === "startDate" || widget.name === "endDate") && widget.type === "time") {
      fieldConfig.format = eventEditFeature.timeFormat;
    }
    Object.assign(widget, fieldConfig);
    return super.processWidgetConfig(widget);
  }
  setupEditorButtons() {
    const { record } = this, { deleteButton } = this.widgetMap;
    if (deleteButton) {
      deleteButton.hidden = this.readOnly || record.isCreating;
    }
  }
  // This will be called if the editor is floating
  onInternalBeforeShow(...args) {
    this.setupUIForEditing();
  }
  // This will be called if the editor is docked
  onBeforeToggleReveal({ reveal }) {
    if (reveal) {
      this.setupUIForEditing();
    }
  }
  setupUIForEditing() {
    const me = this, {
      record,
      titleRenderer
    } = me;
    me.setupEditorButtons();
    me.ariaLabel = me.L("L{Object.editing}", record.name);
    if (titleRenderer) {
      me.title = me.callback(titleRenderer, me, [record]);
    }
  }
  onInternalKeyDown(event) {
    this.trigger("keyDown", { event });
    if (event.key === "Escape") {
      event.stopImmediatePropagation();
      this.eventEditFeature.onCancelClick();
    } else {
      super.onInternalKeyDown(event);
    }
  }
  close(caller) {
    const { eventEditFeature } = this;
    if (!(eventEditFeature == null ? void 0 : eventEditFeature.editingContext) || caller === eventEditFeature) {
      if (!this.isClosing) {
        this.isClosing = true;
        try {
          super.close();
        } finally {
          this.isClosing = false;
        }
      }
    } else {
      eventEditFeature.onCancelClick();
    }
  }
  updateReadOnly(readOnly) {
    const {
      deleteButton,
      saveButton,
      cancelButton
    } = this.widgetMap;
    super.updateReadOnly(readOnly);
    if (deleteButton) {
      deleteButton.hidden = readOnly;
    }
    if (saveButton) {
      saveButton.hidden = readOnly;
    }
    if (cancelButton) {
      cancelButton.hidden = readOnly;
    }
  }
};
// Factoryable type name
__publicField(EventEditor, "type", "eventeditor");
__publicField(EventEditor, "$name", "EventEditor");
__publicField(EventEditor, "configurable", {
  items: [],
  axisLock: "flexible",
  scrollable: {
    // In case editor is very tall or window is small, make it scrollable
    overflowY: true
  },
  readOnly: null,
  /**
   * A Function (or *name* of a function) which produces a customized Panel header based upon the event being edited.
   *
   * @config {Function|String}
   * @param {Scheduler.model.EventModel} eventRecord The record being edited
   * @returns {String} The Panel title.
   */
  titleRenderer: null,
  // We want to maximize on phones and tablets
  maximizeOnMobile: true,
  internalListeners: {
    beforeShow: "onInternalBeforeShow"
  }
});
EventEditor.initClass();
EventEditor._$name = "EventEditor";

// ../Scheduler/lib/Scheduler/view/recurrence/field/RecurrenceFrequencyCombo.js
var RecurrenceFrequencyCombo = class extends Combo {
  buildItems() {
    return [
      ...this.addNone ? [{ text: "L{None}", value: "NONE" }] : [],
      { value: "DAILY", text: "L{Daily}" },
      { value: "WEEKLY", text: "L{Weekly}" },
      { value: "MONTHLY", text: "L{Monthly}" },
      { value: "YEARLY", text: "L{Yearly}" }
    ];
  }
};
__publicField(RecurrenceFrequencyCombo, "$name", "RecurrenceFrequencyCombo");
// Factoryable type name
__publicField(RecurrenceFrequencyCombo, "type", "recurrencefrequencycombo");
__publicField(RecurrenceFrequencyCombo, "configurable", {
  editable: false,
  displayField: "text",
  valueField: "value",
  localizeDisplayFields: true,
  addNone: false,
  // Match the combo size using minWidth, but allow the picker
  // to accommodate long option test.
  picker: {
    align: {
      matchSize: "min"
    }
  }
});
RecurrenceFrequencyCombo.initClass();
RecurrenceFrequencyCombo._$name = "RecurrenceFrequencyCombo";

// ../Scheduler/lib/Scheduler/view/recurrence/field/RecurrenceCombo.js
var RecurrenceCombo = class extends RecurrenceFrequencyCombo {
  static get defaultConfig() {
    return {
      customValue: "custom",
      placeholder: "None",
      splitCls: "b-recurrencecombo-split",
      items: true,
      highlightExternalChange: false
    };
  }
  buildItems() {
    const me = this;
    return [
      { value: "none", text: "L{None}" },
      ...super.buildItems(),
      { value: me.customValue, text: "L{Custom}", cls: me.splitCls }
    ];
  }
  set value(value2) {
    value2 = value2 || "none";
    super.value = value2;
  }
  get value() {
    return super.value;
  }
  set recurrence(recurrence) {
    const me = this;
    if (recurrence) {
      me.value = me.isCustomRecurrence(recurrence) ? me.customValue : recurrence.frequency;
    } else {
      me.value = null;
    }
  }
  isCustomRecurrence(recurrence) {
    const { interval, days: days2, monthDays, months } = recurrence;
    return Boolean(interval > 1 || days2 && days2.length || monthDays && monthDays.length || months && months.length);
  }
};
__publicField(RecurrenceCombo, "$name", "RecurrenceCombo");
// Factoryable type name
__publicField(RecurrenceCombo, "type", "recurrencecombo");
RecurrenceCombo.initClass();
RecurrenceCombo._$name = "RecurrenceCombo";

// ../Scheduler/lib/Scheduler/view/recurrence/RecurrenceLegendButton.js
var RecurrenceLegendButton = class extends Button {
  static get defaultConfig() {
    return {
      localizableProperties: [],
      recurrence: null
    };
  }
  /**
   * Sets / gets the recurrence to display description for.
   * @property {Scheduler.model.RecurrenceModel}
   */
  set recurrence(recurrence) {
    this._recurrence = recurrence;
    this.updateLegend();
  }
  get recurrence() {
    return this._recurrence;
  }
  set eventStartDate(eventStartDate) {
    this._eventStartDate = eventStartDate;
    this.updateLegend();
  }
  get eventStartDate() {
    return this._eventStartDate;
  }
  updateLegend() {
    const { recurrence } = this;
    this.text = recurrence ? RecurrenceLegend.getLegend(recurrence, this.eventStartDate) : "";
  }
  onLocaleChange() {
    this.updateLegend();
  }
  updateLocalization() {
    this.onLocaleChange();
    super.updateLocalization();
  }
};
__publicField(RecurrenceLegendButton, "$name", "RecurrenceLegendButton");
// Factoryable type name
__publicField(RecurrenceLegendButton, "type", "recurrencelegendbutton");
RecurrenceLegendButton.initClass();
RecurrenceLegendButton._$name = "RecurrenceLegendButton";

// ../Scheduler/lib/Scheduler/view/recurrence/field/RecurrenceDaysCombo.js
var RecurrenceDaysCombo = class extends Combo {
  static get defaultConfig() {
    const allDaysValueAsArray = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"], allDaysValue = allDaysValueAsArray.join(",");
    return {
      allDaysValue,
      editable: false,
      defaultValue: allDaysValue,
      workingDaysValue: allDaysValueAsArray.filter((day2, index) => !DateHelper.nonWorkingDays[index]).join(","),
      nonWorkingDaysValue: allDaysValueAsArray.filter((day2, index) => DateHelper.nonWorkingDays[index]).join(","),
      splitCls: "b-recurrencedays-split",
      displayField: "text",
      valueField: "value"
    };
  }
  buildItems() {
    const me = this;
    me._weekDays = null;
    return me.weekDays.concat([
      { value: me.allDaysValue, text: me.L("L{day}"), cls: me.splitCls },
      { value: me.workingDaysValue, text: me.L("L{weekday}") },
      { value: me.nonWorkingDaysValue, text: me.L("L{weekend day}") }
    ]);
  }
  get weekDays() {
    const me = this;
    if (!me._weekDays) {
      const weekStartDay = DateHelper.weekStartDay;
      const dayNames = DateHelper.getDayNames().map((text, index) => ({ text, value: RecurrenceDayRuleEncoder.encodeDay(index) }));
      me._weekDays = dayNames.slice(weekStartDay).concat(dayNames.slice(0, weekStartDay));
    }
    return me._weekDays;
  }
  set value(value2) {
    const me = this;
    if (value2 && Array.isArray(value2)) {
      value2 = value2.join(",");
    }
    if (!value2 || !me.store.findRecord("value", value2)) {
      value2 = me.defaultValue;
    }
    super.value = value2;
  }
  get value() {
    let value2 = super.value;
    if (value2 && Array.isArray(value2)) {
      value2 = value2.join(",");
    }
    return value2;
  }
};
__publicField(RecurrenceDaysCombo, "$name", "RecurrenceDaysCombo");
// Factoryable type name
__publicField(RecurrenceDaysCombo, "type", "recurrencedayscombo");
RecurrenceDaysCombo.initClass();
RecurrenceDaysCombo._$name = "RecurrenceDaysCombo";

// ../Scheduler/lib/Scheduler/widget/DayButtons.js
var DayButtons = class extends ButtonGroup {
  updateItemText(item) {
    const day2 = RecurrenceDayRuleEncoder.decodeDay(item.value)[0], dayName2 = DateHelper.getDayName(day2);
    item.text = dayName2.substring(0, this.dayNameLength);
    item.tooltip = dayName2;
  }
  changeItems(items2) {
    if (items2 === true) {
      const weekStartDay = DateHelper.weekStartDay, dayNames = DateHelper.getDayNames().map((text, index) => ({
        text: text.substring(0, this.dayNameLength),
        tooltip: text,
        value: RecurrenceDayRuleEncoder.encodeDay(index),
        pressedCls: this.pressedCls,
        dataset: {
          dayIndex: index
        }
      }));
      items2 = dayNames.slice(weekStartDay).concat(dayNames.slice(0, weekStartDay));
      items2 = super.changeItems(items2);
    }
    return items2;
  }
  set value(value2) {
    if ((value2 == null ? void 0 : value2.length) > 0 && typeof value2[0] === "number") {
      value2 = value2.map((val) => RecurrenceDayRuleEncoder.encodeDay(val));
    }
    if (value2 && Array.isArray(value2)) {
      value2 = value2.join(this.separator);
    }
    super.value = value2;
  }
  get value() {
    let value2 = super.value;
    if (value2 && Array.isArray(value2)) {
      value2 = value2.join(this.separator);
    }
    return value2;
  }
  /**
   * An array of the selected days, represented as JS day numbers (0 - Sunday, 1 - Monday etc.).
   * @property {Number[]}
   * @readonly
   */
  get valueAsDayNumbers() {
    return this.value ? this.value.split(this.separator).map((value2) => RecurrenceDayRuleEncoder.decodeDay(value2)[0]) : [];
  }
  onLocaleChange() {
    this.items.forEach(this.updateItemText, this);
  }
  updateLocalization() {
    this.onLocaleChange();
    super.updateLocalization();
  }
};
__publicField(DayButtons, "$name", "DayButtons");
// Factoryable type name
__publicField(DayButtons, "type", "daybuttons");
__publicField(DayButtons, "defaultConfig", {
  /**
   * The value for this button group can be set as either an array of strings (e.g., [‘SU’, ‘WE’, ‘TH’]), or
   * an array of day numbers (where 0 represents Sunday).
   *
   * ```javascript
   * new DayButtons({
   *    appendTo : document.body,
   *    value : [0,1]
   * })
   * ```
   * @accepts {Number[]|String[]}
   * @prp {String[]}
   */
  value: null,
  items: true
});
__publicField(DayButtons, "configurable", {
  /**
   * @hideconfigs items, add, remove, insert, remove, removeAll
   */
  /**
   * A CSS class to add to the pressed state of the buttons.
   * @config {String}
   * @default
   */
  pressedCls: "b-raised b-blue",
  /**
   * Number of chars to display for each day
   * @config {Number}
   * @default
   */
  dayNameLength: 1,
  separator: ",",
  defaults: {
    toggleable: true
  }
});
DayButtons.initClass();
DayButtons._$name = "DayButtons";

// ../Scheduler/lib/Scheduler/view/recurrence/field/RecurrenceDaysButtonGroup.js
var RecurrenceDaysButtonGroup = class extends DayButtons {
};
__publicField(RecurrenceDaysButtonGroup, "$name", "RecurrenceDaysButtonGroup");
// Factoryable type name
__publicField(RecurrenceDaysButtonGroup, "type", "recurrencedaysbuttongroup");
RecurrenceDaysButtonGroup.initClass();
RecurrenceDaysButtonGroup._$name = "RecurrenceDaysButtonGroup";

// ../Scheduler/lib/Scheduler/view/recurrence/field/RecurrenceMonthDaysButtonGroup.js
var RecurrenceMonthDaysButtonGroup = class extends ButtonGroup {
  get minValue() {
    return 1;
  }
  get maxValue() {
    return 31;
  }
  construct(config = {}) {
    config.items = this.buildItems();
    super.construct(config);
  }
  buildItems() {
    const items2 = [];
    for (let value2 = this.minValue; value2 <= this.maxValue; value2++) {
      items2.push({
        text: value2 + "",
        value: value2
      });
    }
    return items2;
  }
};
__publicField(RecurrenceMonthDaysButtonGroup, "$name", "RecurrenceMonthDaysButtonGroup");
// Factoryable type name
__publicField(RecurrenceMonthDaysButtonGroup, "type", "recurrencemonthdaysbuttongroup");
__publicField(RecurrenceMonthDaysButtonGroup, "configurable", {
  defaults: {
    toggleable: true
  }
});
RecurrenceMonthDaysButtonGroup.initClass();
RecurrenceMonthDaysButtonGroup._$name = "RecurrenceMonthDaysButtonGroup";

// ../Scheduler/lib/Scheduler/view/recurrence/field/RecurrenceMonthsButtonGroup.js
var RecurrenceMonthsButtonGroup = class extends ButtonGroup.mixin(Validatable_default) {
  construct(config = {}) {
    config.items = this.buildItems();
    super.construct(config);
  }
  buildItems() {
    return DateHelper.getMonthNames().map((item, index) => ({
      text: item.substring(0, 3),
      value: index + 1
      // 1-based
    }));
  }
  updateItemText(item) {
    item.text = DateHelper.getMonthName(item.value - 1).substring(0, 3);
  }
  onLocaleChange() {
    this.items.forEach(this.updateItemText, this);
  }
  updateLocalization() {
    this.onLocaleChange();
    super.updateLocalization();
  }
};
__publicField(RecurrenceMonthsButtonGroup, "$name", "RecurrenceMonthsButtonGroup");
__publicField(RecurrenceMonthsButtonGroup, "type", "recurrencemonthsbuttongroup");
__publicField(RecurrenceMonthsButtonGroup, "configurable", {
  defaults: {
    toggleable: true
  }
});
RecurrenceMonthsButtonGroup.initClass();
RecurrenceMonthsButtonGroup._$name = "RecurrenceMonthsButtonGroup";

// ../Scheduler/lib/Scheduler/view/recurrence/field/RecurrenceStopConditionCombo.js
var RecurrenceStopConditionCombo = class extends Combo {
  buildItems() {
    return [
      { value: "never", text: this.L("L{Never}") },
      { value: "count", text: this.L("L{After}") },
      { value: "date", text: this.L("L{On date}") }
    ];
  }
  set value(value2) {
    value2 = value2 || "never";
    super.value = value2;
  }
  get value() {
    return super.value;
  }
  get recurrence() {
    return this._recurrence;
  }
  set recurrence(recurrence) {
    let value2 = null;
    if (recurrence.endDate) {
      value2 = "date";
    } else if (recurrence.count) {
      value2 = "count";
    }
    this._recurrence = recurrence;
    this.value = value2;
  }
};
__publicField(RecurrenceStopConditionCombo, "$name", "RecurrenceStopConditionCombo");
// Factoryable type name
__publicField(RecurrenceStopConditionCombo, "type", "recurrencestopconditioncombo");
__publicField(RecurrenceStopConditionCombo, "configurable", {
  editable: false,
  placeholder: "Never",
  displayField: "text",
  valueField: "value"
});
RecurrenceStopConditionCombo.initClass();
RecurrenceStopConditionCombo._$name = "RecurrenceStopConditionCombo";

// ../Scheduler/lib/Scheduler/view/recurrence/field/RecurrencePositionsCombo.js
var RecurrencePositionsCombo = class extends Combo {
  static get defaultConfig() {
    return {
      editable: false,
      splitCls: "b-recurrencepositions-split",
      displayField: "text",
      valueField: "value",
      defaultValue: 1,
      maxPosition: 5
    };
  }
  buildItems() {
    return Store.new({
      fields: ["value", "text", "cls"],
      data: this.buildDayNumbers().concat([
        { value: "-1", text: this.L("L{position-1}"), cls: this.splitCls }
      ])
    });
  }
  buildDayNumbers() {
    return ArrayHelper.populate(this.maxPosition, (i) => ({ value: i + 1, text: this.L(`position${i + 1}`) }));
  }
  set value(value2) {
    if (value2 && Array.isArray(value2)) {
      value2 = value2.join(",");
    }
    if (!value2 || !this.store.findRecord("value", value2)) {
      value2 = this.defaultValue;
    }
    super.value = value2;
  }
  get value() {
    const value2 = super.value;
    return value2 ? `${value2}`.split(",").map((item) => parseInt(item, 10)) : [];
  }
};
__publicField(RecurrencePositionsCombo, "$name", "RecurrencePositionsCombo");
// Factoryable type name
__publicField(RecurrencePositionsCombo, "type", "recurrencepositionscombo");
RecurrencePositionsCombo.initClass();
RecurrencePositionsCombo._$name = "RecurrencePositionsCombo";

// ../Scheduler/lib/Scheduler/view/recurrence/RecurrenceEditorPanel.js
var RecurrenceEditorPanel = class extends Panel {
  changeItems(items2, oldItems) {
    const result = super.changeItems(items2, oldItems), {
      positionAndDayRadioField,
      monthDaysNumberRadioField,
      monthDaysNumberField
    } = this.widgetMap;
    if (monthDaysNumberRadioField) {
      positionAndDayRadioField.label = this.L("L{RecurrenceEditor.the}");
    } else {
      positionAndDayRadioField.label = this.L("L{RecurrenceEditor.On the}");
      monthDaysNumberField && result.remove(monthDaysNumberField);
    }
    return result;
  }
  setupWidgetConfig(widgetConfig) {
    if (BrowserHelper.isMobile && !("editable" in widgetConfig)) {
      widgetConfig.editable = false;
    }
    return super.setupWidgetConfig(...arguments);
  }
  updateRecord(record) {
    var _a5, _b2, _c2;
    super.updateRecord(record);
    const me = this, {
      frequencyField,
      daysButtonField,
      monthDaysButtonField,
      monthDaysNumberField,
      monthsButtonField,
      monthDaysRadioField,
      monthDaysNumberRadioField,
      positionAndDayRadioField,
      stopRecurrenceField
    } = me.widgetMap;
    if (record) {
      const timeSpanValues = record.getDateValues();
      if (timeSpanValues) {
        if (!((_a5 = record.days) == null ? void 0 : _a5.length)) {
          daysButtonField.value = timeSpanValues.days;
        }
        if (!((_b2 = record.monthDays) == null ? void 0 : _b2.length)) {
          if (monthDaysButtonField) {
            monthDaysButtonField.value = timeSpanValues.monthDays;
          }
          if (monthDaysNumberField) {
            monthDaysNumberField.value = timeSpanValues.monthDays[0];
          }
        }
        if (!((_c2 = record.months) == null ? void 0 : _c2.length) && monthsButtonField) {
          monthsButtonField.value = timeSpanValues.months[0];
        }
      }
      if (daysButtonField.value && record.positions) {
        positionAndDayRadioField.check();
        if (!me.isPainted) {
          monthDaysRadioField.uncheck();
        }
      } else if (monthDaysButtonField == null ? void 0 : monthDaysButtonField.value) {
        if (monthDaysNumberRadioField && !monthDaysNumberRadioField.hidden) {
          monthDaysNumberRadioField.check();
        } else {
          monthDaysRadioField.check();
        }
        if (!me.isPainted || !me.element.isConnected) {
          positionAndDayRadioField.uncheck();
        }
      }
      if (stopRecurrenceField) {
        stopRecurrenceField.recurrence = record;
      }
    } else {
      frequencyField.value = "NONE";
    }
  }
  //#endregion Configs
  // Disabled field does not contribute to values, clear manually
  get valuesCleanState() {
    return {
      frequency: null,
      interval: null,
      days: null,
      months: null,
      monthDays: null,
      positions: null,
      endDate: null,
      count: null
    };
  }
  processValues(result) {
    if ("monthDays" in result) {
      if (typeof result.monthDays === "string") {
        result.monthDays = result.monthDays.split(",");
      } else {
        result.monthDays = ArrayHelper.asArray(result.monthDays);
      }
    }
  }
  get values() {
    const result = { ...this.valuesCleanState, ...super.values };
    this.processValues(result);
    return result;
  }
  set values(values) {
    super.values = values;
  }
  getValues() {
    const result = { ...this.valuesCleanState, ...super.getValues(...arguments) };
    this.processValues(result);
    return result;
  }
  /**
   * Updates the provided recurrence model with the contained form data.
   * If recurrence model is not provided updates the last loaded recurrence model.
   * @internal
   */
  syncEventRecord(recurrence) {
    recurrence = recurrence || this.record;
    if (recurrence) {
      const values = this.getValues((w) => w.name in recurrence && !w.disabled);
      recurrence.set(values);
    }
  }
  toggleStopFields(userAction) {
    var _a5;
    const me = this, stopValue = (_a5 = me.widgetMap.stopRecurrenceField) == null ? void 0 : _a5.value, { countField, endDateField, frequencyField } = me.widgetMap, countIsUsed = stopValue === "count" && frequencyField.value !== "NONE", dateIsUsed = !countIsUsed && stopValue === "date";
    countField._userAction = endDateField._userAction = userAction;
    countField.disabled = countField.hidden = !countIsUsed;
    endDateField.disabled = endDateField.hidden = !dateIsUsed;
    countField._userAction = endDateField._userAction = false;
  }
  isWidgetAvailableForFrequency(widget, frequency = this.widgetMap.frequencyField.value) {
    return !widget.forFrequency || widget.forFrequency.includes(frequency);
  }
  //#region Events
  onFieldChange({ source, userAction }) {
    const { record } = this;
    if (userAction && this.autoUpdateRecord && record) {
      record.isSanitizingSuspended = true;
    }
    super.onFieldChange(...arguments);
    if (userAction && this.autoUpdateRecord && record) {
      record.isSanitizingSuspended = false;
      this.setTimeout({
        fn: () => record.sanitize(),
        delay: 50,
        cancelOutstanding: true
      });
    }
  }
  onFieldBeforeUpdateDisabled({ source, disabled }) {
    if (!disabled && source._userAction) {
      source.triggerFieldChange({ userAction: true });
    }
  }
  onMonthDaysRadioFieldChange({ source, checked, userAction, propagatingUserActionFrom }) {
    const { monthDaysButtonField, emptyMonthsHiddenField } = this.widgetMap;
    if (monthDaysButtonField) {
      monthDaysButtonField._userAction = userAction;
      monthDaysButtonField.disabled = !checked || !this.isWidgetAvailableForFrequency(monthDaysButtonField);
      monthDaysButtonField._userAction = false;
    }
    if (emptyMonthsHiddenField) {
      emptyMonthsHiddenField._userAction = userAction || propagatingUserActionFrom;
      emptyMonthsHiddenField.disabled = checked || !this.isWidgetAvailableForFrequency(emptyMonthsHiddenField);
      emptyMonthsHiddenField._userAction = false;
    }
  }
  onMonthsButtonFieldChange({ value: value2 }) {
    const monthNames = DateHelper.getMonthNames(), { monthDaysNumberField } = this.widgetMap;
    if (value2 && monthDaysNumberField) {
      monthDaysNumberField.hint = value2.split(",").map((month2) => monthNames[month2 - 1]).join(", ");
    }
  }
  onMonthDaysNumberRadioFieldChange({ checked, userAction }) {
    const { monthDaysNumberField } = this.widgetMap;
    monthDaysNumberField._userAction = userAction;
    monthDaysNumberField.disabled = !checked || !this.isWidgetAvailableForFrequency(monthDaysNumberField);
    monthDaysNumberField._userAction = false;
  }
  onPositionAndDayRadioFieldChange({ source, checked, userAction }) {
    const { daysCombo, positionsCombo } = this.widgetMap;
    daysCombo._userAction = positionsCombo._userAction = userAction;
    daysCombo.disabled = positionsCombo.disabled = !checked || !this.isWidgetAvailableForFrequency(daysCombo);
    daysCombo._userAction = positionsCombo._userAction = false;
  }
  onStopRecurrenceFieldChange({ userAction }) {
    this.toggleStopFields(userAction);
  }
  onFrequencyFieldChange({ value: value2, oldValue, valid, userAction }) {
    const me = this, items2 = me.queryAll((w) => "forFrequency" in w), {
      intervalField,
      stopRecurrenceField
    } = me.widgetMap, isNone = value2 === "NONE";
    if (valid && value2) {
      for (const item of items2) {
        item._userAction = userAction;
        item.disabled = item.hidden = !me.isWidgetAvailableForFrequency(item, value2);
        item._userAction = false;
      }
      me.widgetMap.countField.hidden = value2 === "NONE";
      if (stopRecurrenceField) {
        stopRecurrenceField.hidden = isNone;
      }
      if (intervalField) {
        intervalField.hidden = isNone;
        if (!isNone) {
          intervalField.hint = me.L(`L{RecurrenceEditor.${value2}intervalUnit}`);
        }
        if (oldValue === "NONE" && intervalField.value == null) {
          intervalField.value = 1;
        }
      }
      me.toggleFieldsState();
    }
  }
  //#endregion Events
  toggleFieldsState(userAction) {
    const me = this, { widgetMap } = me, { monthDaysRadioField } = widgetMap;
    if (monthDaysRadioField) {
      me.onMonthDaysRadioFieldChange({ checked: monthDaysRadioField.checked, userAction });
    }
    me.onPositionAndDayRadioFieldChange({ checked: widgetMap.positionAndDayRadioField.checked, userAction });
    me.onStopRecurrenceFieldChange({ userAction });
  }
  updateLocalization() {
    const { countField, intervalField, frequencyField } = this.widgetMap;
    countField.hint = this.L("L{RecurrenceEditor.time(s)}");
    if (frequencyField.value && frequencyField.value !== "NONE") {
      intervalField.hint = this.L(`L{RecurrenceEditor.${frequencyField.value}intervalUnit}`);
    }
    super.updateLocalization();
  }
};
__publicField(RecurrenceEditorPanel, "$name", "RecurrenceEditorPanel");
__publicField(RecurrenceEditorPanel, "type", "recurrenceeditorpanel");
//#region Configs
__publicField(RecurrenceEditorPanel, "configurable", {
  focusable: false,
  cls: "b-recurrenceeditor",
  record: false,
  addNone: false,
  strictRecordMapping: true,
  items: {
    frequencyField: {
      type: "recurrencefrequencycombo",
      name: "frequency",
      label: "L{RecurrenceEditor.Frequency}",
      weight: 10,
      onChange: "up.onFrequencyFieldChange",
      addNone: "up.addNone"
    },
    intervalField: {
      type: "numberfield",
      weight: 15,
      name: "interval",
      label: "L{RecurrenceEditor.Every}",
      min: 1,
      required: true
    },
    daysLabel: {
      type: "label",
      text: "L{RecurrenceEditor.Days}",
      weight: 18,
      forFrequency: "WEEKLY",
      cls: "b-button-group-label"
    },
    daysButtonField: {
      type: "daybuttons",
      cls: "b-recurrencedaysbuttongroup",
      weight: 20,
      name: "days",
      forFrequency: "WEEKLY",
      useGap: true,
      required: true
    },
    emptyMonthsHiddenField: {
      type: "field",
      inputType: "hidden",
      name: "monthDays",
      forFrequency: "MONTHLY",
      internalListeners: {
        beforeUpdateDisabled: "up.onFieldBeforeUpdateDisabled",
        // Enforce empty value for this field.
        // It's used specially to reset monthDays when toggling states
        change() {
          this._value = null;
        }
      }
    },
    // the radio button enabling "monthDaysButtonField" in MONTHLY mode
    monthDaysRadioField: {
      type: "checkbox",
      weight: 30,
      toggleGroup: "radio",
      toggleGroupRootElement: ".b-recurrenceeditorpanel",
      forFrequency: "MONTHLY",
      label: "L{RecurrenceEditor.Each}",
      checked: true,
      onChange: "up.onMonthDaysRadioFieldChange",
      cls: "b-label-with-checkbox"
    },
    monthDaysButtonField: {
      type: "recurrencemonthdaysbuttongroup",
      weight: 40,
      name: "monthDays",
      forFrequency: "MONTHLY",
      cls: "b-space-above",
      required: true,
      internalListeners: {
        beforeUpdateDisabled: "up.onFieldBeforeUpdateDisabled"
      }
    },
    // the radio button enabling monthDays number field in YEARLY modes
    monthDaysNumberRadioField: {
      type: "checkbox",
      weight: 53,
      cls: "b-label-with-checkbox",
      toggleGroup: "radio",
      toggleGroupRootElement: ".b-recurrenceeditorpanel",
      forFrequency: "YEARLY",
      label: "L{RecurrenceEditor.on}",
      style: "display:inline-flex;",
      onChange: "up.onMonthDaysNumberRadioFieldChange"
    },
    monthDaysNumberField: {
      type: "numberfield",
      weight: 56,
      flex: 1,
      name: "monthDays",
      forFrequency: "YEARLY",
      min: 1,
      max: 31,
      required: true,
      internalListeners: {
        beforeUpdateDisabled: "up.onFieldBeforeUpdateDisabled"
      }
    },
    monthsLabel: {
      type: "label",
      text: "L{RecurrenceEditor.Months}",
      weight: 57,
      forFrequency: "YEARLY",
      cls: "b-button-group-label"
    },
    monthsButtonField: {
      type: "recurrencemonthsbuttongroup",
      weight: 58,
      cls: "b-space-above b-space-below",
      name: "months",
      forFrequency: "YEARLY",
      onChange: "up.onMonthsButtonFieldChange",
      required: true
    },
    positionAndDayRadioField: {
      type: "checkbox",
      weight: 60,
      cls: "b-label-with-checkbox b-space-below",
      toggleGroup: "radio",
      toggleGroupRootElement: ".b-recurrenceeditorpanel",
      forFrequency: "MONTHLY|YEARLY",
      label: "L{RecurrenceEditor.On the}",
      onChange: "up.onPositionAndDayRadioFieldChange"
    },
    positionsCombo: {
      type: "recurrencepositionscombo",
      weight: 80,
      name: "positions",
      forFrequency: "MONTHLY|YEARLY",
      cls: "b-space-below b-no-span",
      internalListeners: {
        beforeUpdateDisabled: "up.onFieldBeforeUpdateDisabled"
      }
    },
    daysCombo: {
      type: "recurrencedayscombo",
      weight: 90,
      name: "days",
      forFrequency: "MONTHLY|YEARLY",
      cls: "b-space-below b-no-span",
      internalListeners: {
        beforeUpdateDisabled: "up.onFieldBeforeUpdateDisabled"
      }
    },
    stopRecurrenceField: {
      type: "recurrencestopconditioncombo",
      weight: 100,
      label: "L{RecurrenceEditor.End repeat}",
      onChange: "up.onStopRecurrenceFieldChange",
      cls: "b-no-inner-span"
    },
    countField: {
      type: "numberfield",
      weight: 110,
      name: "count",
      min: 2,
      required: true,
      disabled: true,
      label: " ",
      cls: "b-no-span",
      internalListeners: {
        beforeUpdateDisabled: "up.onFieldBeforeUpdateDisabled"
      }
    },
    endDateField: {
      type: "datefield",
      weight: 120,
      name: "endDate",
      hidden: true,
      disabled: true,
      label: " ",
      required: true,
      cls: "b-no-span",
      internalListeners: {
        beforeUpdateDisabled: "up.onFieldBeforeUpdateDisabled"
      }
    }
  }
});
RecurrenceEditorPanel.initClass();
RecurrenceEditorPanel._$name = "RecurrenceEditorPanel";

// ../Scheduler/lib/Scheduler/view/recurrence/RecurrenceEditor.js
var RecurrenceEditor = class extends Popup {
  static get configurable() {
    return {
      draggable: true,
      closable: true,
      floating: true,
      cls: "b-recurrenceeditor",
      title: "L{Repeat event}",
      autoClose: true,
      width: 470,
      items: {
        recurrenceEditorPanel: {
          type: "recurrenceeditorpanel",
          items: {
            monthDaysNumberRadioField: false
          },
          title: null
        }
      },
      bbar: {
        defaults: {
          localeClass: this
        },
        items: {
          foo: {
            type: "widget",
            cls: "b-label-filler",
            weight: 100
          },
          saveButton: {
            color: "b-green",
            text: "L{Save}",
            onClick: "up.onSaveClick",
            weight: 200
          },
          cancelButton: {
            color: "b-gray",
            text: "L{Object.Cancel}",
            onClick: "up.onCancelClick",
            weight: 300
          }
        }
      },
      scrollable: {
        overflowY: true
      }
    };
  }
  updateReadOnly(readOnly) {
    super.updateReadOnly(readOnly);
    this.bbar.hidden = readOnly;
  }
  get recurrenceEditorPanel() {
    return this.widgetMap.recurrenceEditorPanel;
  }
  updateRecord(record) {
    this.recurrenceEditorPanel.record = record;
  }
  onSaveClick() {
    const me = this;
    if (me.saveHandler) {
      me.saveHandler.call(me.thisObj || me, me, me.record);
    } else {
      me.recurrenceEditorPanel.syncEventRecord();
      me.close();
    }
  }
  onCancelClick() {
    const me = this;
    if (me.cancelHandler) {
      me.cancelHandler.call(me.thisObj || me, me, me.record);
    } else {
      me.close();
    }
  }
};
__publicField(RecurrenceEditor, "$name", "RecurrenceEditor");
// Factoryable type name
__publicField(RecurrenceEditor, "type", "recurrenceeditor");
RecurrenceEditor.initClass();
RecurrenceEditor._$name = "RecurrenceEditor";

// ../Scheduler/lib/Scheduler/feature/mixin/RecurringEventEdit.js
var RecurringEventEdit_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    changeEditorConfig(editorConfig) {
      editorConfig.items = { ...editorConfig.items, ...this.recurringEventsItems };
      editorConfig = super.changeEditorConfig(editorConfig);
      return editorConfig;
    }
    doDestroy() {
      var _a6, _b2;
      (_a6 = this._recurrenceConfirmation) == null ? void 0 : _a6.destroy();
      (_b2 = this._recurrenceEditor) == null ? void 0 : _b2.destroy();
      super.doDestroy();
    }
    onEditorConstructed(editor) {
      var _a6;
      const me = this;
      editor.ion({
        hide: me.onRecurringEventEditorHide,
        show: me.onRecurringEventEditorShow,
        // Docked mode
        reveal: me.onRecurringEventEditorShow,
        unreveal: me.onRecurringEventEditorHide,
        thisObj: me
      });
      if (me.editRecurrenceButton && me.client.enableRecurringEvents !== false) {
        me.setTimeout(() => me.editRecurrenceButton.menu = me.recurrenceEditor, 100);
      }
      (_a6 = me.recurrenceCombo) == null ? void 0 : _a6.ion({
        change: me.onRecurrenceComboChange,
        thisObj: me
      });
    }
    updateReadOnly(readOnly) {
      if (this._recurrenceEditor) {
        this._recurrenceEditor.readOnly = readOnly;
      }
    }
    internalShowEditor() {
      this.toggleRecurringFieldsVisibility(this.client.enableRecurringEvents && this.showRecurringUI !== false);
    }
    toggleRecurringFieldsVisibility(show = true) {
      var _a6, _b2, _c2, _d;
      const methodName = show ? "show" : "hide";
      (_b2 = (_a6 = this.editRecurrenceButton) == null ? void 0 : _a6[methodName]) == null ? void 0 : _b2.call(_a6);
      (_d = (_c2 = this.recurrenceCombo) == null ? void 0 : _c2[methodName]) == null ? void 0 : _d.call(_c2);
    }
    onRecurringEventEditorShow({ source }) {
      var _a6;
      if (this.recurrenceCombo && ((_a6 = source.record) == null ? void 0 : _a6.startDate) && !source.record.isRecurring && this.useContextualRecurrenceRules) {
        const me = this, { recurrenceCombo } = me, { store } = recurrenceCombo;
        if (!store.modelClass.fieldMap.isCustom) {
          store.modelClass.addField({
            name: "isCustom",
            defaultValue: false
          });
        }
        const date2 = source.record.startDate, monthDate = date2.getDate(), dayNumber = date2.getDay(), dayName2 = RecurrenceDayRuleEncoder.encodeDay(dayNumber), monthNumber = date2.getMonth(), monthWeek = Math.ceil(monthDate / 7), monthWeekText = me.L(`L{RecurrenceLegend.position${monthWeek}}`), customRules = [
          {
            isCustom: true,
            text: me.L("L{RecurrenceLegend.Weekly on {1}}", { days: DateHelper.getDayName(dayNumber) }),
            value: `WEEKLY`
          },
          {
            isCustom: true,
            text: me.L("L{RecurrenceLegend.Monthly on {1}}", { days: DateHelper.format(date2, "Do") }),
            value: `MONTHLY`
          },
          {
            isCustom: true,
            text: me.L("L{RecurrenceLegend.Monthly on {1}}", { days: `${monthWeekText} ${DateHelper.getDayName(dayNumber)}` }),
            value: `MONTHLY;BYDAY=${dayName2};BYSETPOS=${monthWeek}`
          },
          {
            isCustom: true,
            text: me.L("L{RecurrenceLegend.Yearly on {1} of {2}}", {
              days: DateHelper.format(date2, "Do"),
              months: DateHelper.getMonthName(monthNumber)
            }),
            value: `YEARLY`
          },
          {
            isCustom: true,
            text: me.L("L{RecurrenceLegend.Yearly on {1} of {2}}", {
              days: `${monthWeekText} ${DateHelper.getDayName(dayNumber)}`,
              months: DateHelper.getMonthName(monthNumber)
            }),
            value: `YEARLY;BYDAY=${dayName2};BYMONTH=${monthNumber + 1};BYSETPOS=${monthWeek}`
          }
        ];
        me.defaultRules = store.records.slice();
        store.add(customRules);
        store.filter((r) => r.isCustom || r.value === "none" || r.value === "DAILY");
      }
    }
    onRecurringEventEditorHide() {
      if (this.recurrenceCombo) {
        const { store } = this.recurrenceCombo;
        if (store && this.defaultRules) {
          store.clearFilters();
          store.data = this.defaultRules;
        }
      }
    }
    // Builds RecurrenceModel to load into the recurrenceEditor
    // It builds the model based on either:
    // - recurrence rule string (if provided)
    // - or the event being edited recurrence (if the event is repeating)
    // - or simply make a recurrence model w/ default state (by default means: Frequency=Daily, Interval=1)
    makeRecurrence(rule) {
      const event = this.eventRecord, eventCopy = event.copy();
      let recurrence = event.recurrence;
      if (!rule && recurrence) {
        recurrence = recurrence.copy();
      } else {
        recurrence = new event.recurrenceModel(rule ? { rule } : {});
      }
      if (eventCopy.timeZone != null) {
        recurrence.timeZone = eventCopy.timeZone;
      }
      recurrence.timeSpan = eventCopy;
      eventCopy.setStartDate(this.values.startDate);
      recurrence.suspendTimeSpanNotifying();
      return recurrence;
    }
    onRecurrableEventBeforeSave({
      eventRecord,
      context
    }) {
      const me = this;
      if (me.isEditing && !eventRecord.isCreating && eventRecord.supportsRecurring && (eventRecord.isRecurring || eventRecord.isOccurrence)) {
        me.recurrenceConfirmation.confirm({
          actionType: "update",
          eventRecord,
          changerFn() {
            context.finalize(true);
          },
          cancelFn() {
            context.finalize(false);
          }
        });
        context.async = true;
      }
    }
    changeRecurrenceConfirmation(recurrenceConfirmation, was) {
      if (recurrenceConfirmation) {
        return RecurrenceConfirmationPopup.reconfigure(this.client.recurrenceConfirmationPopup, RecurrenceConfirmationPopup.mergeConfigs({
          owner: this.editor
        }, recurrenceConfirmation), this);
      }
    }
    set recurrenceEditor(recurrenceEditor) {
      this._recurrenceEditor = recurrenceEditor;
    }
    get recurrenceEditor() {
      const me = this;
      let recurrenceEditor = me._recurrenceEditor;
      if (!recurrenceEditor || !recurrenceEditor.$$name) {
        me._recurrenceEditor = recurrenceEditor = Widget.create({
          type: "recurrenceeditor",
          autoShow: false,
          centered: true,
          modal: true,
          // It's used as the Menu of a Button which syncs the width unless it's already set
          minWidth: "auto",
          constrainTo: globalThis,
          anchor: false,
          rootElement: me.rootElement,
          saveHandler: me.recurrenceEditorSaveHandler,
          thisObj: me,
          internalListeners: {
            beforeShow: me.onBeforeShowRecurrenceEditor,
            thisObj: me
          },
          ...recurrenceEditor
        });
        recurrenceEditor.readOnly = me._readOnly;
      }
      return recurrenceEditor;
    }
    onBeforeShowRecurrenceEditor() {
      const me = this, {
        recurrenceEditor,
        eventRecord
      } = me;
      if (recurrenceEditor && (eventRecord == null ? void 0 : eventRecord.supportsRecurring)) {
        if (!me.recurrence) {
          me.recurrence = me.makeRecurrence();
        }
        me.recurrence.timeSpan.setStartDate(me.values.startDate);
        recurrenceEditor.record = me.recurrence;
        recurrenceEditor.centered = true;
      }
    }
    loadRecurrenceData(recurrence) {
      this.recurrence = recurrence;
      this.updateRecurrenceFields(recurrence);
    }
    updateRecurrenceFields(recurrence) {
      const me = this, { editRecurrenceButton } = me;
      if (me.recurrenceCombo && !me.processingRecurrenceComboChange) {
        me.recurrenceCombo.recurrence = recurrence;
      }
      if (editRecurrenceButton) {
        editRecurrenceButton.recurrence = recurrence;
        editRecurrenceButton.value = recurrence ? recurrence.rule : null;
        if (recurrence && me.client.enableRecurringEvents && me.showRecurringUI !== false) {
          editRecurrenceButton.show();
        } else {
          editRecurrenceButton.hide();
        }
      }
    }
    onRecurrenceComboChange({
      source,
      value: value2,
      userAction
    }) {
      if (userAction) {
        const me = this, { recurrenceEditor } = me;
        if (value2 === source.customValue) {
          me.recurrenceCombo.recurrence = me.makeRecurrence();
          if (recurrenceEditor.centered) {
            recurrenceEditor.show();
          } else {
            recurrenceEditor.show((me.editRecurrenceButton || source).element);
          }
        } else {
          me.processingRecurrenceComboChange = true;
          me.loadRecurrenceData(value2 && value2 !== "none" ? me.makeRecurrence(`FREQ=${value2}`) : null);
          me.processingRecurrenceComboChange = false;
        }
      }
    }
    recurrenceEditorSaveHandler(editor, recurrence) {
      editor.recurrenceEditorPanel.syncEventRecord(recurrence);
      const { store } = this.recurrenceCombo;
      store.clearFilters();
      store.remove(store.query((r) => r.isCustom));
      this.updateRecurrenceFields(recurrence);
      editor.close();
    }
    onDatesChange(...args) {
      super.onDatesChange(...args);
      if (!this.loadingRecord && this.editRecurrenceButton) {
        const { startDate } = this.values;
        if (startDate) {
          this.editRecurrenceButton.eventStartDate = startDate;
        }
      }
    }
    internalLoadRecord(eventRecord) {
      if (eventRecord == null ? void 0 : eventRecord.supportsRecurring) {
        this.loadRecurrenceData(eventRecord.recurrence ? this.makeRecurrence() : null);
      }
    }
    updateRecord(record) {
      if (record.recurrenceRule && !this.recurrence) {
        record.recurrenceRule = null;
      }
      return super.updateRecord(record);
    }
  }, __publicField(_a5, "$name", "RecurringEventEdit"), __publicField(_a5, "configurable", {
    recurringEventsItems: {
      /**
       * Reference to the `Repeat` event field, if used
       * @member {Scheduler.view.recurrence.field.RecurrenceCombo} recurrenceCombo
       * @readonly
       */
      recurrenceCombo: {
        type: "recurrencecombo",
        label: "L{EventEdit.Repeat}",
        ref: "recurrenceCombo",
        weight: 700
      },
      /**
       * Reference to the button that opens the event repeat settings dialog, if used
       * @member {Scheduler.view.recurrence.RecurrenceLegendButton} editRecurrenceButton
       * @readonly
       */
      editRecurrenceButton: {
        type: "recurrencelegendbutton",
        ref: "editRecurrenceButton",
        name: "recurrenceRule",
        color: "b-gray",
        menuIcon: null,
        flex: 1,
        weight: 800,
        ignoreParentReadOnly: true
      }
    },
    recurrenceConfirmation: {
      $config: "lazy",
      value: {
        type: "recurrenceconfirmation"
      }
    },
    /**
     * Set to `false` to hide recurring fields in event editor, even if the
     * {@link Scheduler.view.mixin.RecurringEvents#config-enableRecurringEvents Recurring Events} is `true`
     * and a recurring event is being edited.
     * @config {Boolean}
     * @category Recurring
     */
    showRecurringUI: null,
    /**
     * By default, the day of week and week of month of the event's start date are used to create
     * helpful contextual recurrence types when editing a non-recurring event record and choosing
     * an initial recurrence type.
     *
     * Set this to `false` to disable this and always show unconfigured WEEKLY, MONTHLY and YEARLY options.
     * @prp {Boolean}
     * @default true
     */
    useContextualRecurrenceRules: true
  }), _a5;
};

// ../Scheduler/lib/Scheduler/widget/ResourceCombo.js
var ResourceCombo = class extends Combo {
  static get configurable() {
    return {
      /**
       * Show the {@link Scheduler.model.ResourceModel#field-eventColor event color} for each resource
       * @config {Boolean}
       * @default false
       */
      showEventColor: null,
      displayField: "name",
      valueField: "id",
      picker: {
        cls: "b-resourcecombo-picker",
        itemIconTpl(record) {
          if (this.owner.showEventColor) {
            const { eventColor } = record, isStyleColor = !DomHelper.isNamedColor(eventColor), style = eventColor ? isStyleColor ? ` style="color:${eventColor}"` : "" : ' style="display:none"', colorClass = !eventColor || isStyleColor ? "" : ` b-sch-foreground-${eventColor}`;
            return `<div aria-hidden="true" class="b-icon b-icon-square${colorClass}"${style}></div>`;
          }
          if (this.multiSelect) {
            return `<div aria-hidden="true" class="b-icon b-icon-square"></div>`;
          }
          return "";
        }
      }
    };
  }
  changeShowEventColor(showEventColor) {
    return Boolean(showEventColor);
  }
  updateShowEventColor(showEventColor) {
    var _a5;
    this.element.classList.toggle("b-show-event-color", Boolean(showEventColor));
    (_a5 = this._picker) == null ? void 0 : _a5.element.classList.toggle("b-show-event-color", Boolean(showEventColor));
  }
  changePicker(picker, oldPicker) {
    picker = super.changePicker(picker, oldPicker);
    picker == null ? void 0 : picker.element.classList.toggle("b-show-event-color", Boolean(this.showEventColor));
    return picker;
  }
  // Implementation needed at this level because it has two inner elements in its inputWrap
  get innerElements() {
    return [
      {
        class: "b-icon b-resource-icon b-icon-square b-hide-display",
        reference: "resourceIcon"
      },
      this.inputElement
    ];
  }
  syncInputFieldValue() {
    var _a5, _b2;
    const me = this, {
      resourceIcon,
      lastResourceIconCls
    } = me, { classList } = resourceIcon, eventColor = (_b2 = (_a5 = me.selected) == null ? void 0 : _a5.eventColor) != null ? _b2 : "";
    super.syncInputFieldValue();
    resourceIcon.style.color = "";
    lastResourceIconCls && classList.remove(lastResourceIconCls);
    me.lastResourceIconCls = null;
    if (eventColor) {
      if (DomHelper.isNamedColor(eventColor)) {
        me.lastResourceIconCls = `b-sch-foreground-${eventColor}`;
        classList.add(me.lastResourceIconCls);
      } else {
        resourceIcon.style.color = eventColor;
      }
      classList.remove("b-hide-display");
    } else {
      classList.add("b-hide-display");
    }
  }
};
__publicField(ResourceCombo, "$name", "ResourceCombo");
// Factoryable type name
__publicField(ResourceCombo, "type", "resourcecombo");
ResourceCombo.initClass();
ResourceCombo._$name = "ResourceCombo";

// ../Scheduler/lib/Scheduler/feature/EventEdit.js
var punctuation = /[^\w\d]/g;
var _EventEdit = class _EventEdit extends EditBase.mixin(TaskEditStm_default, RecurringEventEdit_default, Delayable_default) {
  static get pluginConfig() {
    return {
      chain: [
        "populateEventMenu",
        "onEventEnterKey"
      ],
      assign: [
        "editEvent"
      ]
    };
  }
  //endregion
  //region Init & destroy
  construct(scheduler, config) {
    this.readOnly = scheduler.readOnly;
    super.construct(scheduler, config);
    scheduler.ion({
      projectChange: "onChangeProject",
      readOnly: "onClientReadOnlyToggle",
      thisObj: this
    });
  }
  get scheduler() {
    return this.client;
  }
  get project() {
    return this.client.project;
  }
  //endregion
  //region Editing
  onClientReadOnlyToggle({ readOnly }) {
    this.readOnly = readOnly;
    this._editor && (this.editor.readOnly = readOnly);
  }
  /**
   * Returns the editor widget representing this feature
   * @member {Core.widget.Popup}
   */
  get editor() {
    var _a5, _b2, _c2, _d, _e;
    const me = this, editorListeners = {
      beforeHide: "resetEditingContext",
      beforeShow: "onBeforeEditorShow",
      keyDown: "onPopupKeyDown",
      thisObj: me
    };
    let { _editor: editor } = me;
    if (editor) {
      return editor;
    }
    editor = me._editor = Widget.create(me.getEditorConfig());
    const {
      startDateField,
      startTimeField,
      endDateField,
      endTimeField
    } = editor.widgetMap;
    if (!startDateField && startTimeField) {
      startTimeField.label = me.L("L{Start}");
      startTimeField.flex = "1 0 100%";
    }
    if (!endDateField && endTimeField) {
      endTimeField.label = me.L("L{End}");
      endTimeField.flex = "1 0 100%";
    }
    if (!editor.floating && !editor.positioned) {
      VersionHelper.deprecate("scheduler", "7.0.0", '"editorConfig" with "appendTo" deprecated in favor of "drawer : true" config');
      if (!editor.element.parentNode) {
        me.client.add(editor);
      }
      delete editorListeners.beforeHide;
      delete editorListeners.beforeShow;
      editorListeners.beforeToggleReveal = "onBeforeEditorToggleReveal";
    }
    editor.readOnly = me.readOnly;
    if (editor.items.length === 0) {
      console.warn("Event Editor configured without any `items`");
    }
    editor.ion(editorListeners);
    me.scheduler.relayEvents(editor, ["beforeSetRecord"], "eventEdit");
    Object.values(editor.widgetMap).forEach((widget) => {
      const ref = widget.ref || widget.id;
      if (ref && !me[ref]) {
        me[ref] = widget;
        switch (widget.name) {
          case "startDate":
          case "endDate":
            widget.ion({ change: "onDatesChange", thisObj: me });
            break;
        }
      }
    });
    (_a5 = me.onEditorConstructed) == null ? void 0 : _a5.call(me, editor);
    (_b2 = me.eventTypeField) == null ? void 0 : _b2.ion({ change: "onEventTypeChange", thisObj: me });
    (_c2 = me.saveButton) == null ? void 0 : _c2.ion({ click: "onSaveClick", thisObj: me });
    (_d = me.deleteButton) == null ? void 0 : _d.ion({ click: "onDeleteClick", thisObj: me });
    (_e = me.cancelButton) == null ? void 0 : _e.ion({ click: "onCancelClick", thisObj: me });
    return editor;
  }
  getEditorConfig() {
    const me = this, { cls, scheduler } = me, result = ObjectHelper.assign({
      owner: scheduler,
      eventEditFeature: me,
      weekStartDay: me.weekStartDay,
      align: "b-t",
      id: `${scheduler.id}-event-editor`,
      autoShow: false,
      anchor: true,
      scrollAction: me.editorConfig.autoUpdateRecord ? null : "realign",
      constrainTo: globalThis,
      cls
    }, me.editorConfig);
    if (Widget.prototype.getRenderContext(result)[0]) {
      result.floating = false;
    }
    if (result.floating === false && !result.positioned) {
      result.collapsible = {
        type: "overlay",
        direction: "right",
        autoClose: false,
        tool: null,
        recollapseTool: null
      };
      result.collapsed = true;
      result.hidden = result.anchor = false;
      result.hide = function() {
        this.collapsible.toggleReveal(false);
      };
    }
    if (!scheduler.showEventColorPickers && result.items.colorField) {
      result.items.colorField.hidden = true;
    }
    result.onElementCreated = me.updateCSSVars.bind(this);
    return result;
  }
  updateCSSVars({ element }) {
    const time = new Date(2e3, 12, 31, 23, 55, 55), dateLength = DateHelper.format(time, this.dateFormat).replace(punctuation, "").length, timeLength = DateHelper.format(time, this.timeFormat).replace(punctuation, "").length, dateTimeLength = dateLength + timeLength;
    element.style.setProperty("--date-time-length", `${dateTimeLength}em`);
    element.style.setProperty("--date-width-difference", `${(dateLength - timeLength) / 2}em`);
  }
  // Called from editEvent() to actually show the editor
  async internalShowEditor(eventRecord, resourceRecord, align = null) {
    var _a5, _b2;
    const me = this, { scheduler, editor } = me, { useInitialAnimation } = scheduler, eventElement = ((_a5 = align == null ? void 0 : align.target) == null ? void 0 : _a5.nodeType) === Element.ELEMENT_NODE ? align.target : scheduler.getElementFromEventRecord(eventRecord, resourceRecord), isPartOfStore = eventRecord.isPartOfStore(scheduler.eventStore);
    align = align != null ? align : {
      // Align to the element (b-sch-event) and not the wrapper
      target: eventElement,
      anchor: true
    };
    if (align.target || (!isPartOfStore || eventRecord.resources.length === 0) || eventRecord.isCreating) {
      scheduler.element.classList.add("b-eventeditor-editing");
      me.resourceRecord = resourceRecord;
      me.editingContext = {
        eventEdit: me,
        eventRecord,
        resourceRecord,
        eventElement,
        editor,
        isPartOfStore
      };
      (_b2 = super.internalShowEditor) == null ? void 0 : _b2.call(this, eventRecord, resourceRecord, align);
      if (me.typeField) {
        me.toggleEventType(eventRecord.getValue(me.typeField));
      }
      me.loadRecord(eventRecord, resourceRecord);
      editor.readOnly = me.readOnly || eventRecord.readOnly || scheduler.readOnly;
      editor.title = editor.readOnly ? me.L("L{Object.information}") : editor.initialTitle;
      if (editor.collapsed) {
        await editor.collapsible.toggleReveal(true);
        editor.focus();
      } else if (editor.centered || !editor.anchor || !editor.floating) {
        editor.show();
      } else if (eventElement && (!eventRecord.isCreating || !useInitialAnimation || useInitialAnimation === true || useInitialAnimation === "fade-in")) {
        me.targetEventElement = eventElement;
        editor.showBy(align);
      } else {
        editor.show();
        editor.updateCentered(true);
      }
      const timeResolution = scheduler.timeAxisViewModel.timeResolution;
      if (timeResolution.unit === "hour" || timeResolution.unit === "minute") {
        const step = `${timeResolution.increment}${timeResolution.unit}`;
        if (me.startTimeField) {
          me.startTimeField.step = step;
        }
        if (me.endTimeField) {
          me.endTimeField.step = step;
        }
      }
      me.detachListeners("changesWhileEditing");
      scheduler.eventStore.ion({
        change: me.onChangeWhileEditing,
        refresh: me.onChangeWhileEditing,
        thisObj: me,
        name: "changesWhileEditing"
      });
    }
  }
  onChangeWhileEditing() {
    const me = this;
    if (!me.editor.autoUpdateRecord && !me.isFinalizingEventSave && me.isEditing && me.editingContext.isPartOfStore && !me.eventRecord.isPartOfStore(me.scheduler.eventStore)) {
      me.onCancelClick();
    }
  }
  // Fired in a listener so that it's after the auto-called onBeforeShow listeners so that
  // subscribers to the beforeEventEditShow are called at exactly the correct lifecycle point.
  onBeforeEditorShow() {
    super.onBeforeEditorShow(...arguments);
    this.scheduler.trigger("beforeEventEditShow", {
      ...this.editingContext
    });
  }
  updateTargetEventElement(targetEventElement, oldTargetEventElement) {
    targetEventElement == null ? void 0 : targetEventElement.classList.add("b-editing");
    oldTargetEventElement == null ? void 0 : oldTargetEventElement.classList.remove("b-editing");
  }
  /**
   * Opens an editor for the passed event. This function is exposed on Scheduler and can be called as
   * `scheduler.editEvent()`.
   * @param {Scheduler.model.EventModel} eventRecord Event to edit
   * @param {Scheduler.model.ResourceModel} [resourceRecord] The Resource record for the event.
   * This parameter is needed if the event is newly created for a resource and has not been assigned, or when using
   * multi assignment.
   * @param {HTMLElement} [element] Element to anchor editor to (defaults to events element)
   * @on-owner
   */
  editEvent(eventRecord, resourceRecord, element = null, stmCapture = null) {
    var _a5, _b2, _c2;
    const me = this, { client } = me, {
      eventStore,
      assignmentStore,
      enableEventAnimations
    } = client, { simpleEventEdit } = client.features;
    if (!eventRecord.isModel) {
      eventRecord = eventStore.createRecord(eventRecord);
    }
    const isNew = !eventRecord.isOccurrence && !eventStore.$master.includes(eventRecord);
    if (isNew) {
      const resourceRecords = [];
      eventRecord.isCreating = true;
      let assignmentRecords = [];
      if (resourceRecord && !assignmentStore.includesAssignment(eventRecord.id, resourceRecord.id)) {
        resourceRecords.push(resourceRecord);
        assignmentRecords = assignmentStore.assignEventToResource(eventRecord, resourceRecord);
      }
      if (client.trigger("beforeEventAdd", { eventRecord, resourceRecords, assignmentRecords }) === false) {
        assignmentStore == null ? void 0 : assignmentStore.remove(assignmentRecords);
        eventRecord.isCreating = false;
        return false;
      }
    }
    if (me.isEditing) {
      me.resetEditingContext();
    }
    if (me.disabled || eventRecord.isCreating && (simpleEventEdit == null ? void 0 : simpleEventEdit.enabled)) {
      return;
    }
    if (client.trigger("beforeEventEdit", {
      eventEdit: me,
      eventRecord,
      resourceRecord,
      eventElement: ((_a5 = client.getElementFromEventRecord) == null ? void 0 : _a5.call(client, eventRecord, resourceRecord)) || element
    }) === false) {
      client.element.classList.remove("b-eventeditor-editing");
      if (client.isCalendar) {
        eventRecord.isCreating = false;
      }
      return false;
    }
    if (stmCapture) {
      me.applyStmCapture(stmCapture);
    } else if (stmCapture !== false && !client.isCalendar && !me.hasStmCapture) {
      me.captureStm(true);
    }
    if (client.refreshRows) {
      if (isNew) {
        me.enableEventAnimations = false;
        eventStore.$master.add(eventRecord);
        client.project.commitAsync().then(() => client.enableEventAnimations = enableEventAnimations);
        client.refreshRows();
      }
      if (eventRecord.isCreating) {
        (_c2 = (_b2 = client.getElementFromEventRecord) == null ? void 0 : _b2.call(client, eventRecord, resourceRecord, true)) == null ? void 0 : _c2.focus({ preventScroll: true });
      }
    }
    return me.doEditEvent(eventRecord, resourceRecord, element, stmCapture).then((result) => {
      if (!me.isDestroying) {
        if (!me.isEditing && !client.isCalendar && !me.rejectingStmTransaction) {
          if (result !== false && me.hasStmCapture) {
            return me.freeStm(false);
          } else {
            return me.freeStm();
          }
        }
      }
    });
  }
  /**
   * Returns true if the editor is currently active
   * @readonly
   * @property {Boolean}
   */
  get isEditing() {
    const { _editor } = this;
    return Boolean(
      // Editor is not visible if it is collapsed and not expanded
      (_editor == null ? void 0 : _editor.isVisible) && !(_editor.collapsed && !_editor.revealed)
    );
  }
  // editEvent is the single entry point in the base class.
  // Subclass implementations of the action may differ, so are implemented in doEditEvent
  async doEditEvent(eventRecord, resourceRecord, element = null) {
    const me = this, { scheduler } = me, isNewRecord = eventRecord.isCreating;
    if (!resourceRecord) {
      resourceRecord = eventRecord.resource || me.resourceStore.getById(eventRecord.resourceId);
    }
    if (isNewRecord) {
      TimeSpan.prototype.normalize.call(eventRecord);
    }
    if (element || isNewRecord || eventRecord.resources.length === 0) {
      return me.internalShowEditor(eventRecord, resourceRecord, element ? {
        target: element
      } : null);
    } else {
      const dimension = scheduler.isVertical ? "maxHeight" : "maxWidth";
      await scheduler.scrollResourceEventIntoView(resourceRecord, eventRecord, {
        [dimension]: me.minEditSize,
        animate: true,
        edgeOffset: 0,
        extendTimeAxis: false
      });
      if (!scheduler.isDestroyed) {
        await me.internalShowEditor(eventRecord, resourceRecord);
        if (!scheduler.isDestroyed) {
          scheduler.element.classList.remove("b-eventeditor-editing");
        }
      }
    }
  }
  /**
   * Sets fields values from record being edited
   * @private
   */
  loadRecord(eventRecord, resourceRecord) {
    this.loadingRecord = true;
    this.internalLoadRecord(eventRecord, resourceRecord);
    this.loadingRecord = false;
  }
  get eventRecord() {
    var _a5;
    return (_a5 = this._editor) == null ? void 0 : _a5.record;
  }
  internalLoadRecord(eventRecord, resourceRecord) {
    var _a5;
    const me = this, { eventStore } = me.client, { editor, resourceField } = me, { endTimeField } = editor.widgetMap;
    me.resourceRecord = resourceRecord;
    if (resourceField && ((_a5 = resourceField.store) == null ? void 0 : _a5.masterStore) !== me.resourceStore) {
      resourceField.store = editor.chainResourceStore();
    }
    if (endTimeField) {
      endTimeField.min = null;
    }
    editor.record = eventRecord;
    if (resourceField) {
      const resources = eventStore.assignmentStore.getResourcesForEvent(eventRecord);
      editor.assigningValues = true;
      if (!eventRecord.isOccurrence && !eventStore.storage.includes(eventRecord, true) && resourceRecord) {
        me.resourceField.value = resourceRecord.getValue(me.resourceField.valueField);
      } else if (me.assignmentStore) {
        me.resourceField.value = resources.map((resource) => resource.getValue(me.resourceField.valueField));
      }
      editor.assigningValues = false;
    }
    super.internalLoadRecord(eventRecord, resourceRecord);
  }
  toggleEventType(eventType) {
    this.editor.element.dataset.eventType = eventType || "";
    this.editor.eachWidget((widget) => {
      var _a5;
      ((_a5 = widget.dataset) == null ? void 0 : _a5.eventType) && (widget.hidden = widget.dataset.eventType !== eventType);
    });
  }
  //endregion
  //region Save
  async finalizeEventSave(eventRecord, resourceRecords, resolve, reject) {
    const me = this, {
      scheduler,
      assignmentStore
    } = me;
    const aborted = false;
    assignmentStore.suspendAutoCommit();
    scheduler.suspendRefresh();
    me.onBeforeSave(eventRecord);
    eventRecord.beginBatch();
    me.updateRecord(eventRecord);
    eventRecord.endBatch();
    if (!eventRecord.isOccurrence) {
      if (me.resourceField) {
        assignmentStore.assignEventToResource(eventRecord, resourceRecords, null, true);
      }
    } else if (resourceRecords) {
      eventRecord.set("resourceRecords", resourceRecords);
    }
    eventRecord.isCreating = false;
    if (!aborted) {
      await scheduler.project.commitAsync();
    }
    assignmentStore.resumeAutoCommit();
    scheduler.resumeRefresh(true);
    if (!aborted) {
      scheduler.trigger("afterEventSave", { eventRecord });
      me.onAfterSave(eventRecord);
    }
    resolve(aborted ? false : eventRecord);
  }
  /**
   * Saves the changes (applies them to record if valid, if invalid editor stays open)
   * @private
   * @fires beforeEventSave
   * @fires beforeEventAdd
   * @fires afterEventSave
   * @async
   */
  save() {
    return new Promise((resolve, reject) => {
      var _a5;
      const me = this, { scheduler, eventRecord } = me;
      if (!eventRecord || !me.editor.isValid) {
        resolve(false);
        return;
      }
      const { eventStore, values } = me, resourceRecords = ((_a5 = me.resourceField) == null ? void 0 : _a5.records) || (me.resourceRecord ? [me.resourceRecord] : []);
      if (eventStore && scheduler.isDateRangeAvailable) {
        let { startDate, endDate } = values;
        if (!endDate) {
          if ("duration" in values) {
            endDate = DateHelper.add(startDate, values.duration, values.durationUnit || eventRecord.durationUnit);
          } else if ("fullDuration" in values) {
            endDate = DateHelper.add(startDate, values.fullDuration);
          } else {
            endDate = eventRecord.endDate;
          }
        }
        const valid = scheduler.isDateRangeAvailable(startDate, endDate, eventRecord, resourceRecords);
        if (!valid) {
          const { startDateField } = this.editor.widgetMap;
          if (startDateField) {
            startDateField.setError(me.L("L{EventDrag.eventOverlapsExisting}"), false, true);
            startDateField.focus();
          }
          resolve(false);
          return;
        }
      }
      const context = {
        finalize(saveEvent) {
          try {
            if (saveEvent !== false) {
              me.finalizeEventSave(eventRecord, resourceRecords, resolve, reject);
            } else {
              resolve(false);
            }
          } catch (e) {
            reject(e);
          }
        }
      };
      const triggerResult = scheduler.trigger("beforeEventSave", {
        eventRecord,
        resourceRecords,
        values,
        context
      });
      function handleEventResult(result, eventRecord2, context2) {
        if (result === false) {
          resolve(false);
        } else {
          me.onRecurrableEventBeforeSave({ eventRecord: eventRecord2, context: context2 });
          if (!context2.async) {
            context2.finalize();
          }
        }
      }
      if (ObjectHelper.isPromise(triggerResult)) {
        triggerResult.then((result) => handleEventResult(result, eventRecord, context));
      } else {
        handleEventResult(triggerResult, eventRecord, context);
      }
    });
  }
  //endregion
  //region Delete
  /**
   * Delete event being edited
   * @fires beforeEventDelete
   * @private
   * @async
   */
  async deleteEvent() {
    const { editor } = this, { autoClose } = editor;
    this.detachListeners("changesWhileEditing");
    return await this.scheduler.removeEvents([this.eventRecord], this.client.isScheduler ? (removeRecord) => {
      if (removeRecord && editor.containsFocus) {
        editor.autoClose = false;
        editor.revertFocus();
        editor.autoClose = autoClose;
      }
    } : null, editor);
  }
  //endregion
  //region Stores
  onChangeProject() {
    if (this.resourceField) {
      this.resourceField.store = {};
    }
  }
  get eventStore() {
    return this.scheduler.project.eventStore;
  }
  get resourceStore() {
    return this.scheduler.project.resourceStore;
  }
  get assignmentStore() {
    return this.scheduler.project.assignmentStore;
  }
  //endregion
  //endregion
  //region Events
  onActivateEditor({ eventRecord, resourceRecord, eventElement, domEvent }) {
    var _a5;
    const { ignoreSelector } = this;
    if (!ignoreSelector || !((_a5 = domEvent.target) == null ? void 0 : _a5.closest(ignoreSelector))) {
      this.editEvent(eventRecord, resourceRecord, eventElement);
    }
  }
  onDragCreateEnd({ eventRecord, resourceRecord, proxyElement, stmCapture }) {
    this.editEvent(eventRecord, resourceRecord, proxyElement, stmCapture);
  }
  // chained from EventNavigation
  onEventEnterKey({ assignmentRecord, eventRecord, target }) {
    const { client } = this, element = target[target.matches(client.eventSelector) ? "querySelector" : "closest"](client.eventInnerSelector);
    if (assignmentRecord) {
      this.editEvent(eventRecord, assignmentRecord.resource, element);
    } else if (eventRecord) {
      this.editEvent(eventRecord, eventRecord.resource, element);
    }
  }
  // Toggle fields visibility when changing eventType
  onEventTypeChange({ value: value2 }) {
    this.toggleEventType(value2);
  }
  //endregion
  //region Context menu
  populateEventMenu({ eventRecord, resourceRecord, items: items2 }) {
    const me = this, canEdit = !me.readOnly && !me.scheduler.readOnly && !eventRecord.readOnly && !me.disabled;
    items2.editEvent = {
      text: "L{EventEdit.Edit event}",
      localeClass: me,
      icon: "b-icon b-icon-edit",
      weight: 100,
      hidden: !canEdit,
      onItem: () => me.editEvent(eventRecord, resourceRecord)
    };
    items2.showDetails = {
      text: "L{Object.showDetails}",
      localeClass: me,
      icon: "b-icon b-icon-show-details",
      weight: 100,
      hidden: canEdit,
      onItem: () => me.editEvent(eventRecord, resourceRecord)
    };
  }
  //endregion
  // Deprecated code. Remove in 7.0
  // This handles the case where the editor is not floating and not appended to the scheduler.
  // This approach is deprecated in favour of the drawer config and will be removed in 7.0
  onBeforeEditorToggleReveal({ reveal }) {
    if (reveal) {
      this.editor.setupEditorButtons();
    }
    this[reveal ? "onBeforeEditorShow" : "resetEditingContext"]();
  }
  async resetEditingContext() {
    const me = this;
    me.detachListeners("changesWhileEditing");
    super.resetEditingContext();
    if (me.hasStmCapture && !me.isDeletingEvent && !me.isCancelingEdit) {
      await me.freeStm(false);
    }
    me.resourceRecord = null;
  }
  finalizeStmCapture(shouldReject) {
    return this.freeStm(!shouldReject);
  }
  updateLocalization() {
    if (this._editor) {
      this.updateCSSVars({ element: this._editor.element });
    }
    super.updateLocalization(...arguments);
  }
};
//region Config
__publicField(_EventEdit, "$name", "EventEdit");
__publicField(_EventEdit, "configurable", {
  /**
   * The event that shall trigger showing the editor. Defaults to `eventdblclick`, set to `''` or null to
   * disable editing of existing events.
   * @config {String}
   * @default
   * @category Editor
   */
  triggerEvent: "eventdblclick",
  /**
   * A CSS selector targeting elements that should not trigger the editor when clicked.
   * @config {String}
   * @category Editor
   */
  ignoreSelector: null,
  /**
   * The data field in the model that defines the eventType.
   * Applied as class (b-eventtype-xx) to the editors element, to allow showing/hiding fields depending on
   * eventType. Dynamic toggling of fields in the editor is activated by adding an `eventTypeField` field to
   * your widget:
   *
   * ```javascript
   * const scheduler = new Scheduler({
   *    features : {
   *       eventEdit : {
   *           items : {
   *               eventTypeField : {
   *                  type  : 'combo',
   *                  name  : 'eventType',
   *                  label : 'Type',
   *                  items : ['Appointment', 'Internal', 'Meeting']
   *               }
   *           }
   *        }
   *     }
   * });
   * ```
   * Note, your event model class also must declare this field:
   * ```javascript
   *  class MyEvent extends EventModel {
   *      static get fields() {
   *          return [
   *              { name : 'eventType' }
   *          ];
   *      }
   *  }
   * ```
   * @config {String}
   * @default
   * @category Editor
   */
  typeField: "eventType",
  /**
   * The current {@link Scheduler.model.EventModel} record, which is being edited by the event editor.
   * @property {Scheduler.model.EventModel}
   * @readonly
   */
  eventRecord: null,
  /**
   * Specify `true` to put the editor in read only mode.
   *
   * It will also be in read only mode if the Scheduler / Calendar is so, or if the event record is read only.
   *
   * @prp {Boolean}
   * @default false
   */
  readOnly: null,
  /**
   * The configuration for the internal editor widget. With this config you can control the *type*
   * of editor (defaults to `Popup`) and which widgets to show,
   * change the items in the `bbar`, or change whether the popup should be modal etc.
   *
   * ```javascript
   * const scheduler = new Scheduler({
   *     features : {
   *         eventEdit  : {
   *             editorConfig : {
   *                 modal  : true,
   *                 cls    : 'my-editor' // A CSS class,
   *                 items  : {
   *                     owner : {
   *                         weight : -100, // Will sort above system-supplied fields which are weight 100 to 800
   *                         type   : 'usercombo',
   *                         name   : 'owner',
   *                         label  : 'Owner'
   *                     },
   *                     agreement : {
   *                         weight : 1000, // Will sort below system-supplied fields which are weight 100 to 800
   *                         type   : 'checkbox',
   *                         name   : 'agreement',
   *                         label  : 'Agree to terms'
   *                     },
   *                     resourceField : {
   *                         // Apply a special filter to limit the Combo's access
   *                         // to resources.
   *                         store  {
   *                             filters : [{
   *                                 filterBy(resource) {
   *                                     return shouldShowResource(record);
   *                                 }
   *                             }]
   *                         }
   *                     }
   *                 },
   *                 bbar : {
   *                     items : {
   *                         deleteButton : {
   *                             hidden : true
   *                         }
   *                     }
   *                 }
   *             }
   *         }
   *     }
   * });
   * ```
   *
   * Or to use your own custom editor:
   *
   * ```javascript
   * const scheduler = new Scheduler({
   *     features : {
   *         eventEdit  : {
   *             editorConfig : {
   *                 type : 'myCustomEditorType'
   *             }
   *         }
   *     }
   * });
   * ```
   * @config {EventEditorConfig}
   * @category Editor
   */
  editorConfig: {
    type: "eventeditor",
    title: "L{EventEdit.Edit event}",
    closable: true,
    localeClass: _EventEdit,
    defaults: {
      localeClass: _EventEdit
    },
    items: {
      /**
       * Reference to the name field, if used
       * @member {Core.widget.TextField} nameField
       * @readonly
       */
      nameField: {
        type: "text",
        label: "L{Name}",
        clearable: true,
        name: "name",
        weight: 100
      },
      /**
       * Reference to the resource field, if used
       * @member {Core.widget.Combo} resourceField
       * @readonly
       */
      resourceField: {
        type: "resourcecombo",
        label: "L{Resource}",
        name: "resource",
        editable: true,
        valueField: "id",
        displayField: "name",
        highlightExternalChange: false,
        destroyStore: true,
        weight: 200
      },
      /**
       * Reference to the start date field, if used
       * @member {Core.widget.DateField} startDateField
       * @readonly
       */
      startDateField: {
        type: "date",
        clearable: false,
        required: true,
        label: "L{Start}",
        name: "startDate",
        validateDateOnly: true,
        weight: 300
      },
      /**
       * Reference to the start time field, if used
       * @member {Core.widget.TimeField} startTimeField
       * @readonly
       */
      startTimeField: {
        type: "timefield",
        clearable: false,
        required: true,
        ariaLabel: "L{startTime}",
        name: "startDate",
        cls: "b-match-label",
        weight: 400
      },
      /**
       * Reference to the end date field, if used
       * @member {Core.widget.DateField} endDateField
       * @readonly
       */
      endDateField: {
        type: "date",
        clearable: false,
        required: true,
        label: "L{End}",
        name: "endDate",
        validateDateOnly: true,
        weight: 500
      },
      /**
       * Reference to the end time field, if used
       * @member {Core.widget.TimeField} endTimeField
       * @readonly
       */
      endTimeField: {
        type: "timefield",
        clearable: false,
        required: true,
        ariaLabel: "L{endTime}",
        name: "endDate",
        cls: "b-match-label",
        weight: 600
      },
      colorField: {
        label: "L{SchedulerBase.color}",
        type: "eventColorField",
        name: "eventColor",
        weight: 700
      }
    },
    bbar: {
      // When readOnly, child buttons are hidden
      hideWhenEmpty: true,
      defaults: {
        localeClass: _EventEdit
      },
      items: {
        /**
         * Reference to the save button, if used
         * @member {Core.widget.Button} saveButton
         * @readonly
         */
        saveButton: {
          color: "b-blue",
          cls: "b-raised",
          text: "L{Save}",
          weight: 100
        },
        /**
         * Reference to the delete button, if used
         * @member {Core.widget.Button} deleteButton
         * @readonly
         */
        deleteButton: {
          text: "L{Delete}",
          weight: 200
        },
        /**
         * Reference to the cancel button, if used
         * @member {Core.widget.Button} cancelButton
         * @readonly
         */
        cancelButton: {
          text: "L{Object.Cancel}",
          weight: 300
        }
      }
    }
  },
  targetEventElement: null,
  /**
   * How much of a long event bar which is clipped by scrolling must be brought into view
   * to facilitate editing.
   *
   * In a horizontal Scheduler, this will bring 100 pixels of width into view.
   *
   * In a vertical Scheduler, this will bring 100 pixels of height into view.
   * @prp {Number}
   * @defult
   */
  minEditSize: 100
});
var EventEdit = _EventEdit;
EventEdit._$name = "EventEdit";
GridFeatureManager.registerFeature(EventEdit, true, "Scheduler");
GridFeatureManager.registerFeature(EventEdit, false, ["SchedulerPro", "ResourceHistogram"]);
EventEdit.initClass();

// ../Scheduler/lib/Scheduler/feature/EventFilter.js
var EventFilter = class extends InstancePlugin {
  static get pluginConfig() {
    return {
      chain: ["populateTimeAxisHeaderMenu"]
    };
  }
  /**
   * Populates the header context menu items.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Object<String,MenuItemConfig|Boolean|null>} options.items A named object to describe menu items
   * @internal
   */
  populateTimeAxisHeaderMenu({ items: items2 }) {
    const me = this;
    items2.eventsFilter = {
      text: "L{filterEvents}",
      icon: "b-fw-icon b-icon-filter",
      disabled: me.disabled,
      localeClass: me,
      weight: 100,
      menu: {
        localeClass: me,
        cls: "b-eventfilter-menu",
        items: {
          nameFilter: {
            weight: 110,
            type: "textfield",
            cls: "b-eventfilter b-last-row",
            clearable: true,
            keyStrokeChangeDelay: 300,
            label: "L{byName}",
            localeClass: me,
            width: 200,
            internalListeners: {
              change: me.onEventFilterChange,
              thisObj: me
            }
          }
        },
        onBeforeShow({ source: menu }) {
          const [filterByName] = menu.items, filter2 = me.store.filters.getBy("property", "name");
          filterByName.value = (filter2 == null ? void 0 : filter2.value) || "";
        }
      }
    };
  }
  onEventFilterChange({ value: value2 }) {
    if (value2 !== "") {
      this.store.filter("name", value2);
    } else {
      this.store.removeFilter("name");
    }
  }
  get store() {
    const { client } = this;
    return client.isGanttBase ? client.store : client.eventStore;
  }
};
__publicField(EventFilter, "$name", "EventFilter");
EventFilter.featureClass = "b-event-filter";
EventFilter._$name = "EventFilter";
GridFeatureManager.registerFeature(EventFilter, true, ["Scheduler", "Gantt"]);
GridFeatureManager.registerFeature(EventFilter, false, "ResourceHistogram");

// ../Scheduler/lib/Scheduler/feature/EventMenu.js
var EventMenu = class extends TimeSpanMenuBase {
  /**
   * @member {SchedulerMenuContext} menuContext
   * An informational object containing contextual information about the last activation of the context menu.
   * @readonly
   */
  static get configurable() {
    return {
      /**
       * A function called before displaying the menu that allows manipulations of its items.
       * Returning `false` from this function prevents the menu being shown.
       *
       * ```javascript
       * features         : {
       *    eventMenu : {
       *         processItems({ items, eventRecord, assignmentRecord, resourceRecord }) {
       *             // Add or hide existing items here as needed
       *             items.myAction = {
       *                 text   : 'Cool action',
       *                 icon   : 'b-fa b-fa-fw b-fa-ban',
       *                 onItem : () => console.log(`Clicked ${eventRecord.name}`),
       *                 weight : 1000 // Move to end
       *             };
       *
       *            if (!eventRecord.allowDelete) {
       *                 items.deleteEvent.hidden = true;
       *             }
       *         }
       *     }
       * },
       * ```
       *
       * @config {Function}
       * @param {Object} context An object with information about the menu being shown.
       * @param {Scheduler.feature.EventMenu} context.feature A reference to this feature.
       * @param {Event} context.domEvent The initiating event.
       * @param {Event} context.event DEPRECATED: The initiating event.
       * @param {Number[]} context.point The client `X` and `Y` position of the initiating event.
       * @param {HTMLElement} context.targetElement The target to which the menu is being applied.
       * @param {Scheduler.model.EventModel} context.eventRecord The record representing the current event.
       * @param {Scheduler.model.ResourceModel} context.resourceRecord The record representing the current resource.
       * @param {Scheduler.model.AssignmentModel} context.assignmentRecord The assignment record.
       * @param {Object<String,MenuItemConfig|Boolean>} context.items An object containing the
       *   {@link Core.widget.MenuItem menu item} configs keyed by their id.
       * @returns {Boolean|null} Returning `false` from this function prevents the menu being shown.
       * @preventable
       */
      processItems: null,
      type: "event"
      /**
       * This is a preconfigured set of items used to create the default context menu. The default options are
       * listed at the top of the page.
       *
       * To remove existing items, set corresponding keys `null`:
       *
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         eventMenu : {
       *             items : {
       *                 deleteEvent   : null,
       *                 unassignEvent : null
       *             }
       *         }
       *     }
       * });
       * ```
       *
       * See the feature config in the above example for details.
       *
       * @config {Object<String,MenuItemConfig|Boolean|null>} items
       */
    };
  }
  static get pluginConfig() {
    const config = super.pluginConfig;
    config.chain.push("populateEventMenu");
    return config;
  }
  //endregion
  //region Events
  /**
   * This event fires on the owning Scheduler before the context menu is shown for an event. Allows manipulation of the items
   * to show in the same way as in `processItems`. Returning `false` from a listener prevents the menu from
   * being shown.
   * @event eventMenuBeforeShow
   * @on-owner
   * @preventable
   * @param {Scheduler.view.Scheduler} source
   * @param {Object<String,MenuItemConfig>} items Menu item configs
   * @param {Scheduler.model.EventModel} eventRecord Event record for which the menu was triggered
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record, if assignments are used
   * @param {HTMLElement} eventElement
   * @param {MouseEvent} [event] Pointer event which triggered the context menu (if any)
   */
  /**
   * This event fires on the owning Scheduler when an item is selected in the context menu.
   * @event eventMenuItem
   * @on-owner
   * @param {Scheduler.view.Scheduler} source
   * @param {Core.widget.MenuItem} item
   * @param {Scheduler.model.EventModel} eventRecord
   * @param {Scheduler.model.ResourceModel} resourceRecord
   * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record, if assignments are used
   * @param {HTMLElement} eventElement
   */
  /**
   * This event fires on the owning Scheduler after showing the context menu for an event
   * @event eventMenuShow
   * @on-owner
   * @param {Scheduler.view.Scheduler} source
   * @param {Core.widget.Menu} menu The menu
   * @param {Scheduler.model.EventModel} eventRecord Event record for which the menu was triggered
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record, if assignments are used
   * @param {HTMLElement} eventElement
   */
  //endregion
  get resourceStore() {
    return this.client.isHorizontal ? this.client.store : this.client.resourceStore;
  }
  getDataFromEvent(event) {
    var _a5;
    const data = super.getDataFromEvent(event), eventElement = data.targetElement, { client } = this, eventRecord = client.resolveEventRecord(eventElement), resourceRecord = eventRecord && ((_a5 = client.resolveResourceRecord(eventElement) || this.resourceStore.last) == null ? void 0 : _a5.$original), assignmentRecord = eventRecord && client.resolveAssignmentRecord(eventElement);
    return Object.assign(data, {
      date: client.getDateFromXY([event.pageX, event.pageY], null, false),
      eventElement,
      eventRecord,
      resourceRecord,
      assignmentRecord
    });
  }
  getTargetElementFromEvent({ target }) {
    return target.closest(this.client.eventSelector) || target;
  }
  shouldShowMenu(eventParams) {
    return eventParams.eventRecord;
  }
  /**
   * Shows context menu for the provided event. If record is not rendered (outside of time span/filtered)
   * menu won't appear.
   * @param {Scheduler.model.EventModel} eventRecord Event record to show menu for.
   * @param {Object} [options]
   * @param {HTMLElement} options.targetElement Element to align context menu to.
   * If provided menu will be aligned according to clientX/clientY coordinates.
   * If omitted, context menu will be centered to event element.
   */
  showContextMenuFor(eventRecord, { targetElement } = {}) {
    if (this.disabled) {
      return;
    }
    if (!targetElement) {
      targetElement = this.getElementFromRecord(eventRecord);
      if (!targetElement) {
        return;
      }
    }
    DomHelper.triggerMouseEvent(targetElement, this.triggerEvent);
  }
  getElementFromRecord(record) {
    return this.client.getElementsFromEventRecord(record)[0];
  }
  populateEventMenu({ items: items2, eventRecord, assignmentRecord }) {
    const { client } = this;
    items2.deleteEvent = {
      disabled: eventRecord.readOnly || (assignmentRecord == null ? void 0 : assignmentRecord.readOnly),
      hidden: client.readOnly
    };
    items2.unassignEvent = {
      disabled: eventRecord.readOnly || (assignmentRecord == null ? void 0 : assignmentRecord.readOnly),
      hidden: client.readOnly || client.eventStore.usesSingleAssignment
    };
    if (client.showEventColorPickers || client.showTaskColorPickers) {
      items2.eventColor = {
        disabled: eventRecord.readOnly,
        hidden: client.readOnly
      };
    } else {
      items2.eventColor = {
        hidden: true
      };
    }
  }
  populateItemsWithData({ items: items2, eventRecord }) {
    var _a5, _b2;
    super.populateItemsWithData(...arguments);
    const { client } = this;
    if ((client.showEventColorPickers || client.isSchedulerPro && client.showTaskColorPickers) && ((_a5 = items2.eventColor) == null ? void 0 : _a5.menu)) {
      Objects.merge(items2.eventColor.menu.colorMenu, {
        value: eventRecord.eventColor,
        records: (_b2 = client.selectedRecords) != null ? _b2 : [eventRecord]
      });
    }
  }
  // This generates the fixed, unchanging part of the items and is only called once
  // to generate the baseItems of the feature.
  // The dynamic parts which are set by populateEventMenu have this merged into them.
  changeItems(items2) {
    const { client } = this;
    return Objects.merge({
      deleteEvent: {
        text: "L{SchedulerBase.Delete event}",
        icon: "b-icon b-icon-trash",
        cls: "b-separator",
        weight: 500,
        onItem({ menu, eventRecord }) {
          var _a5;
          const revertTarget = (_a5 = menu.focusInEvent) == null ? void 0 : _a5.relatedTarget;
          if (revertTarget) {
            revertTarget.focus();
            client.navigator.activeItem = revertTarget;
          }
          client.removeEvents(client.isEventSelected(eventRecord) ? client.selectedEvents : [eventRecord]);
        }
      },
      unassignEvent: {
        text: "L{SchedulerBase.Unassign event}",
        icon: "b-icon b-icon-unassign",
        weight: 300,
        onItem({ menu, eventRecord, resourceRecord, assignmentRecord }) {
          var _a5;
          const revertTarget = (_a5 = menu.focusInEvent) == null ? void 0 : _a5.relatedTarget;
          if (revertTarget) {
            revertTarget.focus();
            client.navigator.activeItem = revertTarget;
          }
          if (client.isAssignmentSelected(assignmentRecord)) {
            client.assignmentStore.remove(client.selectedAssignments);
          } else {
            eventRecord.unassign(resourceRecord);
          }
        }
      },
      eventColor: {
        text: "L{SchedulerBase.color}",
        icon: "b-icon b-icon-palette",
        separator: true,
        menu: {
          colorMenu: {
            type: "eventcolorpicker"
          }
        }
      }
    }, items2);
  }
};
//region Config
__publicField(EventMenu, "$name", "EventMenu");
EventMenu.featureClass = "";
EventMenu._$name = "EventMenu";
GridFeatureManager.registerFeature(EventMenu, true, "Scheduler");
GridFeatureManager.registerFeature(EventMenu, false, "ResourceHistogram");

// ../Scheduler/lib/Scheduler/feature/mixin/NonWorkingTimeMixin.js
var NonWorkingTimeMixin_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    getNonWorkingTimeRanges(calendar, startDate, endDate) {
      const maxRange = endDate.getTime() - startDate.getTime() + 24 * 60 * 60 * 1e3;
      if (!calendar.getNonWorkingTimeRanges) {
        const result = [];
        calendar.forEachAvailabilityInterval(
          { startDate, endDate, isForward: true, maxRange },
          (intervalStartDate, intervalEndDate, calendarCacheInterval) => {
            for (const [entry, cache] of calendarCacheInterval.intervalGroups) {
              if (!cache.getIsWorking()) {
                result.push({
                  name: entry.name,
                  iconCls: entry.iconCls,
                  cls: entry.cls,
                  type: entry.type,
                  startDate: intervalStartDate,
                  endDate: intervalEndDate
                });
              }
            }
          }
        );
        return result;
      }
      return calendar.getNonWorkingTimeRanges(startDate, endDate, maxRange);
    }
    getCalendarTimeRanges(calendar, ignoreName = false) {
      const me = this, { timeAxis, fillTicks } = me.client, { unit, increment } = timeAxis, shouldPaint = !me.maxTimeAxisUnit || DateHelper.compareUnits(unit, me.maxTimeAxisUnit) <= 0;
      if (calendar && shouldPaint && timeAxis.count) {
        const allRanges = me.getNonWorkingTimeRanges(calendar, timeAxis.startDate, timeAxis.endDate), timeSpans = allRanges.map((interval) => new TimeSpan({
          name: interval.type !== "Week" ? interval.name : "",
          cls: `b-nonworkingtime ${interval.cls || ""}`,
          startDate: interval.startDate,
          endDate: interval.endDate
        }));
        let mergedSpans = [];
        let prevRange = null;
        for (const range of timeSpans) {
          if (prevRange && range.startDate <= prevRange.endDate && (ignoreName || range.name === prevRange.name) && range.duration > 0) {
            prevRange.endDate = range.endDate;
          } else {
            mergedSpans.push(range);
            range.setData("id", `nonworking-${mergedSpans.length}`);
            prevRange = range;
          }
        }
        if (fillTicks) {
          mergedSpans = mergedSpans.filter(
            (span) => DateHelper.ceil(span.startDate, { magnitude: increment, unit }) < DateHelper.floor(span.endDate, { magnitude: increment, unit })
          );
          mergedSpans.forEach(
            (span) => span.setStartEndDate(
              DateHelper.ceil(span.startDate, { magnitude: increment, unit }),
              DateHelper.floor(span.endDate, { magnitude: increment, unit })
            )
          );
        }
        return mergedSpans;
      } else {
        return [];
      }
    }
    //region Basic scheduler calendar
    setupDefaultCalendar() {
      const { project } = this;
      if (
        // Might have been set up by NonWorkingTime / EventNonWorkingTime already
        !this.autoGeneratedWeekends && // For basic scheduler...
        project.isSchedulerCoreProjectMixin && // ...that uses the default calendar...
        project.effectiveCalendar === project.defaultCalendar && // ...and has no defined intervals
        !project.defaultCalendar.intervalStore.count
      ) {
        this.autoGeneratedWeekends = true;
        this.updateDefaultCalendar();
      }
    }
    updateDefaultCalendar() {
      if (this.autoGeneratedWeekends) {
        const calendar = this.client.project.effectiveCalendar, intervals = this.defaultNonWorkingIntervals, hasIntervals = Boolean(intervals.length);
        calendar.ignoreTimeZone = true;
        calendar.clearIntervals(hasIntervals);
        if (hasIntervals) {
          calendar.addIntervals(intervals);
        }
      }
    }
    updateLocalization() {
      var _a6;
      (_a6 = super.updateLocalization) == null ? void 0 : _a6.call(this);
      this.autoGeneratedWeekends && this.updateDefaultCalendar();
    }
    get defaultNonWorkingIntervals() {
      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      return DateHelper.nonWorkingDaysAsArray.map((dayIndex) => ({
        recurrentStartDate: `on ${dayNames[dayIndex]} at 0:00`,
        recurrentEndDate: `on ${dayNames[(dayIndex + 1) % 7]} at 0:00`,
        isWorking: false
      }));
    }
    //endregion
  }, __publicField(_a5, "$name", "NonWorkingTimeMixin"), __publicField(_a5, "configurable", {
    /**
     * The maximum time axis unit to display non-working ranges for ('hour' or 'day' etc).
     * When zooming to a view with a larger unit, no non-working time elements will be rendered.
     *
     * **Note:** Be careful with setting this config to big units like 'year'. When doing this,
     * make sure the timeline {@link Scheduler/view/TimelineBase#config-startDate start} and
     * {@link Scheduler/view/TimelineBase#config-endDate end} dates are set tightly.
     * When using a long range (for example many years) with non-working time elements rendered per hour,
     * you will end up with millions of elements, impacting performance.
     * When zooming, use the {@link Scheduler/view/mixin/TimelineZoomable#config-zoomKeepsOriginalTimespan} config.
     * @config {DurationUnit}
     * @default
     */
    maxTimeAxisUnit: "week"
  }), _a5;
};

// ../Scheduler/lib/Scheduler/feature/EventNonWorkingTime.js
var EventNonWorkingTime = class extends InstancePlugin.mixin(AttachToProjectMixin_default, NonWorkingTimeMixin_default) {
  static get pluginConfig() {
    return {
      chain: [
        "onTimeAxisViewModelUpdate",
        "updateLocalization",
        "onEventDataGenerated"
      ]
    };
  }
  // Cannot use `static properties = {}`, new Map would pollute the prototype
  static get properties() {
    return {
      calendarRangeCache: /* @__PURE__ */ new Map()
    };
  }
  doDisable(disable) {
    this.client.refresh();
    super.doDisable(disable);
  }
  attachToCalendarManagerStore(store) {
    super.attachToCalendarManagerStore(store);
    const me = this;
    store.ion({
      name: "calendarManagerStore",
      change: "onCalendarChange",
      thisObj: me
    });
    if (me.client.isScheduler) {
      me.project.effectiveCalendar.intervals.ion({
        name: "calendarManagerStore",
        change: "onCalendarChange",
        thisObj: me
      });
    }
    me.setupDefaultCalendar();
  }
  onCalendarChange() {
    this.calendarRangeCache.clear();
  }
  onTimeAxisViewModelUpdate() {
    this.calendarRangeCache.clear();
  }
  //region Draw
  getRangeDomConfig(timeRange, minDate, maxDate, relativeTo) {
    const me = this, { client } = me, startPos = client.getVirtualCoordFromDate(DateHelper.max(timeRange.startDate, minDate)) - relativeTo, endPos = timeRange.endDate ? client.getVirtualCoordFromDate(DateHelper.min(timeRange.endDate, maxDate)) - relativeTo : startPos, size = Math.abs(endPos - startPos), posProp = client.isVertical ? "insetBlockStart" : "insetInlineStart", sizeProp = client.isVertical ? "height" : "width";
    return {
      className: {
        // Borrow styling from time ranges
        "b-sch-timerange": 1,
        "b-sch-range": 1,
        "b-sch-nonworkingtime": 1,
        [timeRange.cls]: timeRange.cls,
        "b-rtl": client.rtl
      },
      dataset: {
        id: timeRange.id
      },
      elementData: {
        timeRange
      },
      style: {
        [posProp]: startPos,
        [sizeProp]: size > 0 ? size : null
      }
    };
  }
  getCalendarTimeRanges(calendar) {
    let cached = this.calendarRangeCache.get(calendar);
    if (!cached) {
      cached = super.getCalendarTimeRanges(calendar, true);
      this.calendarRangeCache.set(calendar, cached);
    }
    return cached;
  }
  // Render ranges into event bars
  onEventDataGenerated({ eventRecord, startMS, endMS, start, end, left, top, children, bufferBeforeWidth }) {
    var _a5;
    if (this.enabled) {
      const { isVertical } = this.client, calendar = (_a5 = eventRecord.effectiveCalendarsCombination) != null ? _a5 : this.client.project.calendar;
      let relativeTo = isVertical ? top : left;
      if (bufferBeforeWidth) {
        relativeTo += bufferBeforeWidth;
      }
      for (const timeRange of this.getCalendarTimeRanges(calendar)) {
        if (timeRange.startDateMS < endMS && timeRange.endDateMS > startMS) {
          const domConfig = this.getRangeDomConfig(timeRange, start, end, relativeTo);
          children.push(domConfig);
        }
      }
    }
  }
  //endregion
};
__publicField(EventNonWorkingTime, "$name", "EventNonWorkingTime");
EventNonWorkingTime._$name = "EventNonWorkingTime";
GridFeatureManager.registerFeature(EventNonWorkingTime, false, "Scheduler");

// ../Scheduler/lib/Scheduler/feature/EventTooltip.js
var zeroOffset = [0, 0];
var depOffset = [
  null,
  [0, 10],
  [10, 0]
];
var EventTooltip = class extends TooltipBase {
  static get defaultConfig() {
    return {
      /**
       * Set this value to `false` to keep Tooltip visible after mouse leaves the target element.
       * @config {Boolean} autoHide
       * @default true
       */
      /**
       * A function which receives data about the event and returns a string,
       * or a Promise yielding a string (for async tooltips), to be displayed in the tooltip.
       * This method will be called with an object containing the fields below
       *
       * @config {Function} template
       * @param {Object} data Data context
       * @param {Scheduler.model.EventModel} data.eventRecord Hovered event bar's record
       * @param {Scheduler.model.AssignmentModel} data.assignmentRecord Hovered event bar's associated assignment
       * @param {Scheduler.model.ResourceModel} data.resourceRecord Hovered event bar's associated resource
       * @param {Date} data.startDate Hovered event bar's start date
       * @param {Date} data.endDate Hovered event bar's end date
       * @param {String} data.startText Start text
       * @param {String} data.endText End text
       * @param {Core.widget.Tooltip} data.tip Current tooltip instance
       * @param {String} data.startClockHtml Predefined HTML to show the start time
       * @param {String} data.endClockHtml Predefined HTML to show the end time
       * @returns {DomConfig|String|null}
       *
       * @category Rendering
       */
      template: (data) => `
                ${data.eventRecord.name ? StringHelper.xss`<div class="b-sch-event-title">${data.eventRecord.name}</div>` : ""}
                ${data.startClockHtml}
                ${data.endClockHtml}`,
      cls: "b-sch-event-tooltip",
      monitorRecordUpdate: true,
      /**
       * Defines what to do if document is scrolled while the tooltip is visible.
       *
       * Valid values: ´null´: do nothing, ´hide´: hide the tooltip or ´realign´: realign to the target if possible.
       *
       * @config {'hide'|'realign'|null}
       * @default
       */
      scrollAction: "hide"
    };
  }
  /**
   * The event which the tooltip feature has been activated for.
   * @member {Scheduler.model.EventModel} eventRecord
   * @readonly
   */
  //endregion
  construct(client, config) {
    super.construct(client, config);
    if (typeof this.align === "string") {
      this.align = { align: this.align };
    }
  }
  // https://github.com/bryntum/support/issues/9562
  /**
   * Augment tooltip with extra data
   *
   * @param {Core.widget.Tooltip} tooltip
   * @param {Event} activeTarget
   * @internal
   * @returns {{resourceRecord: (Scheduler.model.ResourceModel|*), assignmentRecord: (*|Scheduler.model.AssignmentModel)}}
   */
  resolveData(tooltip, activeTarget) {
    const client = this.client, data = super.resolveData(tooltip, activeTarget);
    return data && {
      ...data,
      resourceRecord: client.resolveResourceRecord(activeTarget),
      assignmentRecord: client.resolveAssignmentRecord(activeTarget)
    };
  }
  onInternalPaint({ firstPaint }) {
    super.onInternalPaint(...arguments);
    if (firstPaint) {
      const { dependencies } = this.client.features;
      if (dependencies) {
        this.tooltip.ion({
          beforeAlign({ source: tooltip, offset = zeroOffset }) {
            const { edgeAligned } = parseAlign(tooltip.align.align), depTerminalOffset = dependencies.disabled || !dependencies.allowCreate ? zeroOffset : depOffset[edgeAligned];
            arguments[0].offset = [
              offset[0] + depTerminalOffset[0],
              offset[1] + depTerminalOffset[1]
            ];
          }
        });
      }
    }
  }
};
//region Config
__publicField(EventTooltip, "$name", "EventTooltip");
EventTooltip._$name = "EventTooltip";
GridFeatureManager.registerFeature(EventTooltip, true, "Scheduler");
GridFeatureManager.registerFeature(EventTooltip, false, "ResourceHistogram");

// ../Scheduler/lib/Scheduler/feature/GroupSummary.js
var eventStoreRefreshActions = {
  update: 1,
  filter: 1
};
var assignmentStoreRefreshActions = {
  add: 1,
  remove: 1,
  update: 1,
  removeAll: 1,
  filter: 1
};
var GroupSummary2 = class extends GroupSummary {
  static get properties() {
    return {
      footersToUpdate: /* @__PURE__ */ new Set()
    };
  }
  static get pluginConfig() {
    return {
      chain: ["render"]
    };
  }
  //endregion
  //region Init
  construct(scheduler, config) {
    const me = this;
    if (scheduler.isVertical) {
      throw new Error("GroupSummary feature is not supported in vertical mode");
    }
    me.scheduler = scheduler;
    super.construct(scheduler, config);
    if (!me.summaries && me.renderer) {
      me.summaries = [{ renderer: me.renderer }];
    }
    if (scheduler.isSchedulerBase) {
      scheduler.eventStore.ion({
        changePreCommit: me.onEventStoreChange,
        thisObj: me
      });
      scheduler.assignmentStore.ion({
        changePreCommit: me.onAssignmentStoreChange,
        thisObj: me
      });
      scheduler.ion({
        timeAxisViewModelUpdate: me.onTimeAxisChange,
        thisObj: me
      });
      scheduler.project.ion({
        dataReady: me.onProjectDataReady,
        thisObj: me
      });
    }
  }
  doDestroy() {
    var _a5;
    (_a5 = this._tip) == null ? void 0 : _a5.destroy();
    super.doDestroy();
  }
  //endregion
  //region Events
  onTimeAxisChange() {
    this.scheduler.rowManager.forEach((row) => {
      if (row.isGroupFooter) {
        row.render();
      }
    });
  }
  onEventStoreChange({ action, records, changes }) {
    if (eventStoreRefreshActions[action]) {
      const resources = /* @__PURE__ */ new Set();
      records.forEach((eventRecord) => eventRecord.resources.forEach((r) => resources.add(r)));
      this.afterChange(resources);
    }
  }
  onAssignmentStoreChange({ action, records, changes }) {
    var _a5;
    if (assignmentStoreRefreshActions[action]) {
      const resources = /* @__PURE__ */ new Set();
      records.forEach((assignment) => assignment.resource && resources.add(assignment.resource));
      if (((_a5 = changes == null ? void 0 : changes.resourceId) == null ? void 0 : _a5.oldValue) != null) {
        const oldResource = this.scheduler.resourceStore.getById(changes.resourceId.oldValue);
        oldResource && resources.add(oldResource);
      }
      this.afterChange(resources);
    }
  }
  afterChange(affectedResources) {
    const { resourceStore } = this.scheduler;
    for (const resourceRecord of affectedResources) {
      let record = resourceRecord;
      if (this.target === "header") {
        while (record && !record.isGroupHeader) {
          record = resourceStore.getPrev(record);
        }
      } else {
        while (record && !record.isGroupFooter) {
          record = resourceStore.getNext(record);
        }
      }
      const row = this.scheduler.rowManager.getRowFor(record);
      if (record && row) {
        this.footersToUpdate.add(row);
      }
    }
  }
  onProjectDataReady() {
    var _a5;
    const {
      footersToUpdate,
      client
    } = this;
    if (client.isVisible) {
      if (footersToUpdate.size) {
        for (const footer of footersToUpdate) {
          (_a5 = footer.render) == null ? void 0 : _a5.call(footer);
        }
        footersToUpdate.clear();
      }
    } else {
      client.whenVisible("refresh", client, [true]);
    }
  }
  //endregion
  //region Render
  /**
   * Called before rendering row contents, used to reset rows no longer used as group summary rows
   * @private
   */
  onBeforeRenderRow({ row, record }) {
    if (row.isGroupFooter && !record.meta.hasOwnProperty("groupFooterFor")) {
      const timeaxisCell = row.elements.normal.querySelector(".b-sch-timeaxis-cell");
      if (timeaxisCell) {
        timeaxisCell.innerHTML = "";
      }
    }
    super.onBeforeRenderRow(...arguments);
  }
  /**
   * Called by parent class to fill timeaxis with summary contents. Generates tick "cells" and populates them with
   * summaries.
   * ```
   * <div class="b-timeaxis-group-summary">
   *     <div class="b-timeaxis-tick">
   *         <div class="b-timeaxix-summary-value">x</div>
   *         ...
   *     </div>
   *     ...
   * </div>
   * ```
   * @private
   */
  generateHtml(column, records, cls, groupRecord, groupField, groupValue) {
    if (column.type === "timeAxis") {
      const me = this, { scheduler } = me, { eventStore } = scheduler, colCfg = scheduler.timeAxisViewModel.columnConfig;
      let html = "";
      const eventsByTick = scheduler.getResourcesEventsPerTick(records, ({ event }) => {
        return event.resources.some((resource) => records.includes(resource)) && (!eventStore.isFiltered || eventStore.records.includes(event));
      });
      scheduler.timeAxis.forEach((tick, idx) => {
        const groupEvents = eventsByTick[idx] || [], sumHtml = me.summaries.map((config) => {
          var _a5, _b2;
          const value2 = (_b2 = (_a5 = config.renderer) == null ? void 0 : _a5.call(config, {
            startDate: tick.startDate,
            endDate: tick.endDate,
            resourceStore: scheduler.resourceStore,
            events: groupEvents,
            resources: records,
            eventStore,
            groupRecord,
            groupField,
            groupValue
          })) != null ? _b2 : "";
          return `<div class="b-timeaxis-summary-value">${value2}</div>`;
        }).join("");
        html += `<div class="b-timeaxis-tick" style="width: ${colCfg[colCfg.length - 1][idx].width}px">${sumHtml}</div>`;
      });
      return `<div class="b-timeaxis-group-summary">${html}</div>`;
    }
    return super.generateHtml(column, records, cls);
  }
  /**
   * Overrides parents function to return correct summary count, used when sizing row
   * @private
   */
  updateSummaryHtml(cellElement, column, records) {
    const count = super.updateSummaryHtml(cellElement, column, records);
    if (column.type === "timeAxis") {
      const result = {
        count: 0,
        height: 0
      };
      this.summaries.forEach((config) => {
        if (config.height) {
          result.height += config.height;
        } else {
          result.count++;
        }
      });
      return result;
    }
    return count;
  }
  /**
   * Generates tooltip contents for hovered summary tick
   * @private
   */
  getTipHtml({ activeTarget }) {
    const me = this, { client } = me, groupSummaryValues = [], index = Array.from(activeTarget.parentElement.children).indexOf(activeTarget), tick = client.timeAxis.getAt(index), groupSummaryRecord = client.getRecordFromElement(activeTarget), groupRecord = groupSummaryRecord.isGroupFooter ? groupSummaryRecord.meta.groupRecord : groupSummaryRecord, { eventStore } = client, resources = groupRecord.groupChildren, eventsByTick = client.getResourcesEventsPerTick(resources, ({ event }) => {
      return event.resources.some((resource) => resources.includes(resource)) && (!eventStore.isFiltered || eventStore.includes(event));
    });
    let tipHtml = "", showTip = false;
    DomHelper.forEachSelector(activeTarget, ".b-timeaxis-summary-value", (element, i) => {
      const label = me._labels[i], text = element.innerText.trim();
      groupSummaryValues.push({ label, text });
      if (element.innerHTML) {
        showTip = true;
      }
    });
    tipHtml = me.tooltipTemplate({
      groupSummaryValues,
      startDate: tick.startDate,
      endDate: tick.endDate,
      resourceStore: client.resourceStore,
      events: eventsByTick[index] || [],
      resources,
      eventStore,
      groupRecord,
      groupField: groupRecord.meta.groupField,
      groupValue: groupRecord.meta.groupRowFor
    });
    return showTip ? tipHtml : null;
  }
  /**
  * Renderer function for the grouping summary tooltip.
  *
  * ```javascript
  * new Scheduler({
  *     features : {
  *         groupSummary : {
  *             tooltipTemplate : ({ groupSummaryValues, startDate, endDate, eventStore, resourceStore, events, resources, groupRecord, groupField, groupValue }) => {
  *                 return '<div>My custom tooltip html</div>';
  *             }
  *         }
  *     }
  * });
  * ```
   *
   * @param {GroupSummaryValue[]} groupSummaryValues An array of object with label and value for each summary under the tooltip
   * @param {Date} startDate Tick start date
   * @param {Date} endDate Tick end date
   * @param {Scheduler.model.EventModel[]} events Events which belong to the group
   * @param {Scheduler.model.ResourceModel[]} resources Resources which belong to the group
   * @param {Scheduler.data.EventStore} eventStore Event store
   * @param {Scheduler.data.ResourceStore} resourceStore Resource store
   * @param {Core.data.Model} groupRecord Current group's header row record
   * @param {String} groupField Current group's field name
   * @param {String} groupValue Current group's value
   * @returns {String} Html content
   * @config {Function}
   */
  tooltipTemplate({ groupSummaryValues, startDate, endDate, events, resources, eventStore, resourceStore, groupRecord, groupField, groupValue }) {
    const { client } = this;
    let tipHtml = `<header>${this.L("L{Summary.Summary for}", client.getFormattedDate(startDate))}</header>`;
    groupSummaryValues.forEach(({ label = "", text }) => {
      tipHtml += `<label>${label}</label><div class="b-timeaxis-summary-value">${text}</div>`;
    });
    return tipHtml;
  }
  /**
   * Initialize tooltip on render
   * @private
   */
  render() {
    const me = this, { scheduler } = me;
    if (scheduler.isSchedulerBase) {
      if (me.showTooltip && !me._tip) {
        me._tip = new Tooltip({
          id: `${scheduler.id}-groupsummary-tip`,
          cls: "b-timeaxis-summary-tip",
          hoverDelay: 0,
          hideDelay: 0,
          forElement: scheduler.timeAxisSubGridElement,
          anchorToTarget: true,
          forSelector: ".b-timeaxis-group-summary .b-timeaxis-tick",
          getHtml: me.getTipHtml.bind(me)
        });
      }
    }
  }
  //endregion
  removeSummaryElements(rowEl) {
    const summaryElement = rowEl.querySelector(".b-timeaxis-group-summary");
    summaryElement == null ? void 0 : summaryElement.remove();
    super.removeSummaryElements();
  }
  hasSummary(column) {
    return super.hasSummary(column) || column.isTimeAxisColumn;
  }
  updateSummaries() {
    this._labels = this.summaries.map((config) => config.label || "");
    if (!this.isConfiguring) {
      super.updateSummaries(...arguments);
    }
  }
};
//region Config
__publicField(GroupSummary2, "$name", "GroupSummary");
__publicField(GroupSummary2, "configurable", {
  /**
   * Show tooltip containing summary values and labels
   * @config {Boolean}
   * @default
   */
  showTooltip: true,
  /**
   * Array of summary configs which consists of a label and a {@link #config-renderer} function
   * ```
   *  summaries : [
   *      {
   *         label : 'Label',
   *         renderer : ({ startDate, endDate, eventStore, resourceStore, events, resources, groupRecord, groupField, groupValue }) => {
   *             // return display value
   *             returns '<div>Renderer output</div>';
   *         }
   *      }
   *  ]
   * ```
   * @prp {SchedulerSummaryOptions[]}
   */
  summaries: null,
  /**
   * Renderer function for a single time axis tick in a group summary row.
   * Should calculate a sum and return HTML as a result.
   *
   * <div class="note">
   * You should never modify any records inside this method.
   * </div>
   *
   * ```javascript
   * new Scheduler({
   *     features : {
   *         groupSummary : {
   *             renderer : ({ startDate, endDate, eventStore, resourceStore, events, resources, groupRecord, groupField, groupValue }) => {
   *                 // return display value
   *                 returns '<div>Renderer output</div>';
   *             }
   *         }
   *     }
   * });
   * ```
   *
   * @param {Object} context Rendering context object
   * @param {Date} context.startDate Tick start date
   * @param {Date} context.endDate Tick end date
   * @param {Scheduler.model.EventModel[]} context.events Events which belong to the group
   * @param {Scheduler.model.ResourceModel[]} context.resources Resources which belong to the group
   * @param {Scheduler.data.EventStore} context.eventStore Event store
   * @param {Scheduler.data.ResourceStore} context.resourceStore Resource store
   * @param {Core.data.Model} context.groupRecord Current groups header row record
   * @param {String} context.groupField Current groups field name
   * @param {String} context.groupValue Current groups value
   * @returns {String} Html content
   * @config {Function}
   */
  renderer: null
});
GroupSummary2._$name = "GroupSummary";
GridFeatureManager.registerFeature(GroupSummary2, false, "Scheduler");

// ../Scheduler/lib/Scheduler/feature/mixin/HeaderDragSelect.js
var HeaderDragSelect_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    constructor() {
      super(...arguments);
      __publicField(this, "moveThreshold", 3);
      __publicField(this, "_dragging", false);
    }
    construct() {
      super.construct(...arguments);
      this.chain(this.client, "onElementMouseDown", "onElementMouseDown");
      this.chain(this.client, "onElementMouseMove", "onElementMouseMove");
    }
    onElementMouseDown(event) {
      const me = this;
      if (event.touches || event.button !== 0 || me.disabled || !me.enableDragSelect) {
        return;
      }
      if (event.target.closest(".b-sch-header-timeaxis-cell")) {
        me.startX = event.clientX;
        EventHelper.on({
          element: DomHelper.getRootElement(event.target),
          mouseup: "onMouseUp",
          thisObj: me,
          once: true
        });
      }
    }
    onElementMouseMove(event) {
      const me = this;
      if (typeof me.startX === "number") {
        if (!me._dragging && Math.abs(me.startX - event.clientX) >= me.moveThreshold) {
          const headerEl = me.client.subGrids.normal.header.headersElement;
          me.element = DomHelper.createElement({
            parent: headerEl,
            tag: "div",
            // b-headerzoom-rect for styling backwards compat
            className: "b-header-drag-selection-rect b-headerzoom-rect"
          });
          me.headerElementRect = Rectangle.from(headerEl);
          me._dragging = true;
        }
        if (me.isDragging) {
          const x = Math.max(event.clientX, me.headerElementRect.left), left = Math.min(me.startX, x), width = Math.abs(me.startX - x), rect = new Rectangle(left - me.headerElementRect.x + me.client.scrollLeft, 0, width, me.headerElementRect.height);
          DomHelper.setTranslateX(me.element, rect.left);
          me.element.style.width = rect.width + "px";
        }
      }
    }
    onMouseUp() {
      var _a6;
      const me = this;
      me.startX = null;
      if (me.isDragging) {
        const { client } = me, rect = Rectangle.from(me.element), startDate = client.getDateFromCoordinate(rect.left, "round", false), endDate = client.getDateFromCoordinate(rect.right, "round", false);
        me.element.remove();
        EventHelper.on({
          element: DomHelper.getRootElement(client.element),
          thisObj: me,
          click: (event) => event.stopPropagation(),
          capture: true,
          expires: 50,
          // In case a click did not ensue, remove the listener
          once: true
        });
        me._dragging = null;
        (_a6 = me.onTimeSpanDragSelected) == null ? void 0 : _a6.call(me, { startDate, endDate });
      }
    }
    get isDragging() {
      return this._dragging;
    }
  }, __publicField(_a5, "configurable", {
    enableDragSelect: true
  }), _a5;
};

// ../Scheduler/lib/Scheduler/feature/HeaderZoom.js
var HeaderZoom = class extends InstancePlugin.mixin(HeaderDragSelect_default) {
  onTimeSpanDragSelected({ startDate, endDate }) {
    this.client.zoomToSpan({
      startDate,
      endDate
    });
  }
};
__publicField(HeaderZoom, "$name", "HeaderZoom");
HeaderZoom._$name = "HeaderZoom";
GridFeatureManager.registerFeature(HeaderZoom, false, "Scheduler");

// ../Scheduler/lib/Scheduler/feature/Labels.js
var sides = [
  "top",
  "before",
  "after",
  "bottom"
];
var editorAlign = (side, client) => {
  switch (side) {
    case "top":
      return "b-b";
    case "after":
      return client.rtl ? "r-r" : "l-l";
    case "right":
      return "l-l";
    case "bottom":
      return "t-t";
    case "before":
      return client.rtl ? "l-l" : "r-r";
    case "left":
      return "r-r";
  }
};
var topBottom = {
  top: 1,
  bottom: 1
};
var layoutModes = {
  estimate: 1,
  measure: 1
};
var layoutSides = {
  before: 1,
  after: 1
};
var Labels = class extends InstancePlugin {
  // Plugin configuration. This plugin chains some of the functions in Grid.
  static get pluginConfig() {
    return {
      chain: ["onEventDataGenerated"]
    };
  }
  //endregion
  //region Init & destroy
  construct(scheduler, config) {
    const me = this;
    if (scheduler.isVertical) {
      throw new Error("Labels feature is not supported in vertical mode");
    }
    me.scheduler = scheduler;
    super.construct(scheduler, config);
    if (me.top || me.bottom || me.before || me.after) {
      me.updateHostClasslist();
    }
  }
  updateHostClasslist() {
    const { top, bottom } = this, { classList } = this.scheduler.element;
    classList.remove("b-labels-topbottom");
    classList.remove("b-labels-top");
    classList.remove("b-labels-bottom");
    if (top || bottom) {
      classList.add("b-labels-topbottom");
      if (top) {
        classList.add("b-labels-top");
      }
      if (bottom) {
        classList.add("b-labels-bottom");
      }
    }
  }
  onLabelDblClick(event) {
    var _a5;
    const me = this, target = (_a5 = event.target) == null ? void 0 : _a5.closest("." + me.labelCls);
    if (target && !me.scheduler.readOnly) {
      const { side } = target.dataset, labelConfig = me[side], { editor, field } = labelConfig;
      if (editor) {
        const eventRecord = me.scheduler.resolveEventRecord(event.target);
        if (eventRecord.readOnly) {
          return;
        }
        if (!(editor instanceof Editor)) {
          labelConfig.editor = new Editor({
            blurAction: me.blurAction,
            inputField: editor,
            scrollAction: "realign"
          });
        }
        labelConfig.editor.render(me.scheduler.element);
        labelConfig.editor.startEdit({
          target,
          align: editorAlign(side, me.client),
          matchSize: false,
          record: eventRecord,
          field
        });
        event.stopImmediatePropagation();
        return false;
      }
    }
  }
  changeTop(top) {
    return this.processLabelSpec(top, "top");
  }
  updateTop() {
    this.updateHostClasslist();
  }
  changeAfter(after) {
    return this.processLabelSpec(after, "after");
  }
  updateAfter() {
    this.updateHostClasslist();
  }
  changeRight(right) {
    this[this.client.rtl ? "before" : "after"] = right;
  }
  changeBottom(bottom) {
    return this.processLabelSpec(bottom, "bottom");
  }
  updateBottom() {
    this.updateHostClasslist();
  }
  changeBefore(before) {
    return this.processLabelSpec(before, "before");
  }
  updateBefore() {
    this.updateHostClasslist();
  }
  changeLeft(left) {
    this[this.client.rtl ? "after" : "before"] = left;
  }
  processLabelSpec(labelSpec, side) {
    if (typeof labelSpec === "function") {
      labelSpec = {
        renderer: labelSpec
      };
    } else if (typeof labelSpec === "string") {
      labelSpec = {
        field: labelSpec
      };
    } else if (labelSpec) {
      labelSpec = Object.create(labelSpec);
    } else {
      return null;
    }
    const { scheduler } = this, { eventStore, resourceStore, taskStore, id: id2 } = scheduler, { field, editor } = labelSpec;
    if (topBottom[side]) {
      scheduler.milestoneWidth = null;
    }
    if (eventStore && !taskStore) {
      labelSpec.recordType = "event";
    } else {
      labelSpec.recordType = "task";
    }
    if (field) {
      let fieldDef, fieldFound = false;
      if (eventStore && !taskStore) {
        fieldDef = eventStore.modelClass.fieldMap[field];
        if (fieldDef) {
          labelSpec.fieldDef = fieldDef;
          labelSpec.recordType = "event";
          fieldFound = true;
        } else if (Reflect.has(eventStore.modelClass.prototype, field)) {
          labelSpec.recordType = "event";
          fieldFound = true;
        }
      }
      if (!fieldDef && taskStore) {
        fieldDef = taskStore.modelClass.fieldMap[field];
        if (fieldDef) {
          labelSpec.fieldDef = fieldDef;
          labelSpec.recordType = "task";
          fieldFound = true;
        } else if (Reflect.has(resourceStore.modelClass.prototype, field)) {
          labelSpec.recordType = "task";
          fieldFound = true;
        }
      }
      if (!fieldDef && resourceStore) {
        fieldDef = resourceStore.modelClass.fieldMap[field];
        if (fieldDef) {
          labelSpec.fieldDef = fieldDef;
          labelSpec.recordType = "resource";
          fieldFound = true;
        } else if (Reflect.has(resourceStore.modelClass.prototype, field)) {
          labelSpec.recordType = "resource";
          fieldFound = true;
        }
      }
      if (editor && !this.editListenerAdded) {
        EventHelper.on({
          element: scheduler.timeAxisSubGrid.element,
          delegate: "." + this.labelCls,
          dblclick: "onLabelDblClick",
          thisObj: this
        });
        this.editListenerAdded = true;
      }
    }
    return labelSpec;
  }
  doDisable(disable) {
    super.doDisable(disable);
    if (this.client.isPainted) {
      this.client.refresh();
    }
  }
  //endregion
  generateLabelConfigs(data) {
    var _a5;
    const me = this, configs = [];
    for (const side of sides) {
      if (me[side]) {
        const {
          field,
          fieldDef,
          recordType,
          renderer,
          thisObj
        } = me[side], domConfig = {
          tag: "label",
          className: {
            [me.labelCls]: 1,
            [`${me.labelCls}-${side}`]: 1
          },
          dataset: {
            side,
            taskFeature: `label-${side}`
          }
        };
        let value2;
        const eventRecordProperty = `${recordType}Record`, eventRecord = data[eventRecordProperty];
        if (renderer) {
          value2 = renderer.call(thisObj || me.thisObj || me, {
            [eventRecordProperty]: eventRecord,
            resourceRecord: data.resourceRecord,
            assignmentRecord: data.assignmentRecord,
            domConfig,
            ...me.scheduler.isSchedulerPro ? { eventRecord } : {}
          });
          if (typeof value2 === "object") {
            Object.assign(domConfig, value2);
            value2 = null;
          }
        } else {
          value2 = eventRecord.getValue(field);
          if ((fieldDef == null ? void 0 : fieldDef.type) === "date" && !renderer) {
            value2 = DateHelper.format(value2, me.client.displayDateFormat);
          } else {
            value2 = StringHelper.encodeHtml(value2);
          }
        }
        if (value2 != null && value2 !== false) {
          domConfig.html = value2;
        }
        if (domConfig.html != null || ((_a5 = domConfig.children) == null ? void 0 : _a5.length) || domConfig.text != null) {
          configs.push(domConfig);
        }
      }
    }
    return configs;
  }
  measureLabels(configs, data) {
    const me = this, pxPerMS = me.client.timeAxisViewModel.getSingleUnitInPixels("millisecond");
    for (const config of configs) {
      if (layoutSides[config.dataset.side]) {
        let { html } = config;
        let length = 0;
        if (me.labelLayoutMode === "estimate") {
          if (html.includes("<")) {
            html = DomHelper.stripTags(html);
          }
          length = html.length * me.labelCharWidth + 18;
        } else {
          const element = me.labelMeasureElement || (me.labelMeasureElement = DomHelper.createElement({
            className: "b-sch-event-wrap b-measure-label",
            parent: me.client.foregroundCanvas
          }));
          element.retainElement = true;
          DomSync.sync({
            targetElement: element,
            domConfig: {
              onlyChildren: true,
              children: [
                config
              ]
            }
          });
          length = element.firstElementChild.offsetWidth;
        }
        const ms = length / pxPerMS;
        switch (config.dataset.side) {
          case "before":
            data.startMS -= ms;
            break;
          case "after":
            data.endMS += ms;
            break;
        }
      }
    }
  }
  onEventDataGenerated(data) {
    var _a5;
    if (!this.disabled && !((_a5 = data.eventRecord) == null ? void 0 : _a5.isResourceTimeRange)) {
      const configs = this.generateLabelConfigs(data);
      if (layoutModes[this.labelLayoutMode]) {
        this.measureLabels(configs, data);
      }
      data.wrapperChildren.push(...configs);
    }
  }
  updateLabelLayoutMode() {
    if (!this.isConfiguring) {
      this.client.refreshWithTransition();
    }
  }
  updateLabelCharWidth() {
    if (!this.isConfiguring) {
      this.client.refreshWithTransition();
    }
  }
};
//region Config
__publicField(Labels, "$name", "Labels");
__publicField(Labels, "configurable", {
  /**
   * CSS class to apply to label elements
   * @config {String}
   * @default
   */
  labelCls: "b-sch-label",
  /**
   * Top label configuration object.
   * @config {SchedulerLabelConfig}
   */
  top: null,
  /**
   * Configuration object for the label which appears *after* the event bar in the current writing direction.
   * @config {SchedulerLabelConfig}
   */
  after: null,
  /**
   * Right label configuration object.
   * @config {SchedulerLabelConfig}
   */
  right: null,
  /**
   * Bottom label configuration object.
   * @config {SchedulerLabelConfig}
   */
  bottom: null,
  /**
   * Configuration object for the label which appears *before* the event bar in the current writing direction.
   * @config {SchedulerLabelConfig}
   */
  before: null,
  /**
   * Left label configuration object.
   * @config {SchedulerLabelConfig}
   */
  left: null,
  thisObj: null,
  /**
   * What action should be taken when focus moves leaves the cell editor, for example when clicking outside.
   * May be `'complete'` or `'cancel`'.
   * @config {'complete'|'cancel'}
   * @default
   */
  blurAction: "cancel",
  /**
   * How to handle labels during event layout. Options are:
   *
   * * default - Labels do not affect event layout, events will overlap labels
   * * estimate - Label width is estimated by multiplying text length with {@link #config-labelCharWidth}
   * * measure - Label width is determined by measuring the label, precise but slow
   *
   * Note that this only applies to the left and right labels, top and bottom labels does not take part in the
   * event layout process.
   *
   * @config {'default'|'estimate'|'measure'}
   * @default
   */
  labelLayoutMode: "default",
  /**
   * Factor representing the average char width in pixels used to determine label width when configured
   * with `labelLayoutMode: 'estimate'`.
   * @config {Number}
   * @default
   */
  labelCharWidth: 7
});
Labels.featureClass = "b-sch-labels";
Labels._$name = "Labels";
GridFeatureManager.registerFeature(Labels, false, "Scheduler");

// ../Scheduler/lib/Scheduler/feature/NonWorkingTime.js
var NonWorkingTime = class extends AbstractTimeRanges.mixin(AttachToProjectMixin_default, NonWorkingTimeMixin_default) {
  /**
   * @hideconfigs enableResizing, store
   */
  static get defaultConfig() {
    return {
      /**
       * The feature by default does not render ranges smaller than the base unit used by the time axis.
       * Set this config to `false` to disable this behavior.
       *
       * <div class="note">The {@link #config-maxTimeAxisUnit} config defines a zoom level at which to bail out of
       * rendering ranges completely.</div>
       * @config {Boolean}
       * @default
       */
      hideRangesOnZooming: true,
      showHeaderElements: true,
      showLabelInBody: true,
      autoGeneratedWeekends: false
    };
  }
  static get pluginConfig() {
    const config = super.pluginConfig;
    config.chain.push("attachToProject", "onConfigChange");
    return config;
  }
  //endregion
  //region Init & destroy
  doDestroy() {
    this.attachToCalendar(null);
    super.doDestroy();
  }
  onConfigChange({ name }) {
    if (!this.isConfiguring && name === "fillTicks") {
      this.refresh();
    }
  }
  //endregion
  //region Project
  attachToProject(project) {
    super.attachToProject(project);
    this.attachToCalendar(project.effectiveCalendar);
    if (!project.graph && !this.client.isScheduler) {
      project.ion({
        name: "project",
        dataReady: { fn: () => this.attachToCalendar(project.effectiveCalendar), once: true },
        thisObj: this
      });
    }
    project.ion({
      name: "project",
      calendarChange: () => this.attachToCalendar(project.effectiveCalendar),
      thisObj: this
    });
  }
  //endregion
  //region TimeAxisViewModel
  onTimeAxisViewModelUpdate(...args) {
    this._timeAxisUnitDurationMs = null;
    return super.onTimeAxisViewModelUpdate(...args);
  }
  //endregion
  //region Calendar
  attachToCalendar(calendar) {
    const me = this, { project, client } = me;
    me.detachListeners("calendar");
    me.autoGeneratedWeekends = false;
    if (calendar) {
      me.setupDefaultCalendar();
      calendar.intervalStore.ion({
        name: "calendar",
        change: () => me.setTimeout(() => me.refresh(), 1),
        thisObj: me
      });
    }
    if (client.isEngineReady && !client.project.isDelayingCalculation && !client.isDestroying) {
      me.refresh();
    } else if (!project.isDestroyed) {
      me.detachListeners("initialProjectListener");
      project.ion({
        name: "initialProjectListener",
        refresh({ isCalculated }) {
          if (isCalculated !== false) {
            me.refresh();
            me.detachListeners("initialProjectListener");
          }
        },
        thisObj: me
      });
    }
  }
  get calendar() {
    var _a5;
    return (_a5 = this.project) == null ? void 0 : _a5.effectiveCalendar;
  }
  //endregion
  //region Draw
  get timeAxisUnitDurationMs() {
    if (!this._timeAxisUnitDurationMs) {
      this._timeAxisUnitDurationMs = DateHelper.as("ms", 1, this.client.timeAxis.unit);
    }
    return this._timeAxisUnitDurationMs;
  }
  /**
   * Based on this method result the feature decides whether the provided non-working period should
   * be rendered or not.
   * The method checks that the range has non-zero {@link Scheduler.model.TimeSpan#field-duration},
   * lays in the visible timespan and its duration is longer or equal the base timeaxis unit
   * (if {@link #config-hideRangesOnZooming} is `true`).
   *
   * Override the method to implement your custom range rendering vetoing logic.
   * @param {Scheduler.model.TimeSpan} range Range to render.
   * @returns {Boolean} `true` if the range should be rendered and `false` otherwise.
   */
  shouldRenderRange(range, start, end) {
    return super.shouldRenderRange(range, start, end) && (!this.hideRangesOnZooming || range.durationMS >= this.timeAxisUnitDurationMs);
  }
  // Calendar intervals as TimeSpans, with adjacent intervals merged to create fewer
  get timeRanges() {
    const me = this;
    if (!me._timeRanges) {
      me._timeRanges = me.getCalendarTimeRanges(me.calendar);
    }
    return me._timeRanges;
  }
  //endregion
};
//region Default config
__publicField(NonWorkingTime, "$name", "NonWorkingTime");
NonWorkingTime._$name = "NonWorkingTime";
GridFeatureManager.registerFeature(NonWorkingTime, false, "Scheduler");
GridFeatureManager.registerFeature(NonWorkingTime, true, ["SchedulerPro", "Gantt", "ResourceHistogram"]);

// ../Scheduler/lib/Scheduler/feature/Pan.js
var Pan = class extends InstancePlugin {
  get targetSelector() {
    return `.b-sch-timeaxis-cell,.b-tasknonworkingtime,.b-sch-resourcetimerange,.b-timeline-subgrid${this.enableInHeader ? ",.b-sch-header-timeaxis-cell,.b-sch-header-text" : ""}`;
  }
  //endregion
  //region Plugin config
  static get pluginConfig() {
    return {
      chain: ["onElementMouseDown"]
    };
  }
  //endregion
  // region Events
  /**
   * Fires on the owning Scheduler or Gantt widget before pan starts. Return `false` to prevent the operation.
   * @event beforePan
   * @preventable
   * @on-owner
   * @param {Event} event The native browser DOM event
   */
  //endregion
  onElementMouseDown(event) {
    var _a5;
    const me = this, { client } = me, { button, touches, target, clientX, clientY } = event, dragFeature = client.features.taskDrag || client.features.eventDrag, enablePanOnEvents = client.readOnly || !(dragFeature == null ? void 0 : dragFeature.enabled);
    if (touches || button !== 0 || me.disabled || client.trigger("beforePan", { event }) === false) {
      return;
    }
    if (target.matches(me.targetSelector) || enablePanOnEvents && target.closest(client.eventSelector)) {
      me.mouseX = clientX;
      me.mouseY = clientY;
      me.onHeader = me.enableInHeader && target.closest(".b-sch-header-timeaxis-cell");
      (_a5 = me.mouseDetacher) == null ? void 0 : _a5.call(me);
      me.mouseDetacher = EventHelper.on({
        element: DomHelper.getRootElement(client.element),
        mousemove: "onMouseMove",
        mouseup: "onMouseUp",
        thisObj: me
      });
    }
  }
  onMouseMove(event) {
    const me = this, { client } = me, xScroller = client.timeAxisSubGrid.scrollable, yScroller = client.scrollable, x = event.clientX, y = event.clientY;
    if (event.buttons === 0) {
      me.onMouseUp();
      return;
    }
    event.preventDefault();
    if (me.vertical && (client.isVertical || !me.onHeader)) {
      yScroller.scrollBy(0, me.mouseY - y);
    }
    if (me.horizontal && (client.isHorizontal || !me.onHeader)) {
      xScroller.scrollBy(me.mouseX - x);
    }
    if (!client.eventSelectionDisabled) {
      client.eventSelectionDisabled = true;
      me.panControllingEventSelection = true;
    }
    me.mouseX = x;
    me.mouseY = y;
  }
  onMouseUp() {
    var _a5;
    const { client } = this;
    (_a5 = this.mouseDetacher) == null ? void 0 : _a5.call(this);
    this.mouseDetacher = null;
    client.setTimeout(() => {
      if (this.panControllingEventSelection) {
        client.eventSelectionDisabled = false;
        client.panControllingEventSelection = false;
      }
    }, 50);
  }
  /**
   * Yields `true` if a pan gesture is in process.
   * @member {Boolean} isActive
   * @readonly
   */
  get isActive() {
    return Boolean(this.mouseDetacher);
  }
  //endregion
};
// region Init
__publicField(Pan, "$name", "Pan");
__publicField(Pan, "configurable", {
  /**
   * Set to `false` to not pan horizontally
   * @prp {Boolean}
   * @default
   */
  horizontal: true,
  /**
   * Set to `false` to not pan vertically
   * @prp {Boolean}
   * @default
   */
  vertical: true,
  /**
   * Set to `false` to not pan horizontally when dragging in the time axis header
   * @prp {Boolean}
   * @default
   */
  enableInHeader: true
});
Pan._$name = "Pan";
GridFeatureManager.registerFeature(Pan, false, ["Scheduler", "Gantt"]);

// ../Scheduler/lib/Scheduler/feature/ResourceMenu.js
var ResourceMenu = class extends ContextMenuBase {
  static get pluginConfig() {
    const config = super.pluginConfig;
    config.chain.push("populateResourceMenu");
    return config;
  }
  //endregion
  //region Events
  /**
   * This event fires on the owning scheduler before the context menu is shown for a resource.
   * Allows manipulation of the items to show in the same way as in the {@link #config-processItems}.
   *
   * Returning `false` from a listener prevents the menu from being shown.
   *
   * @event resourceMenuBeforeShow
   * @preventable
   * @on-owner
   * @param {Scheduler.view.Scheduler} source The grid
   * @param {Core.widget.Menu} menu The menu
   * @param {Object<String,MenuItemConfig>} items Menu item configs
   * @param {Grid.column.Column} column Column
   * @param {Scheduler.model.ResourceModel} resourceRecord Record
   */
  /**
   * This event fires on the owning scheduler after the context menu is shown for a resource.
   * @event resourceMenuShow
   * @on-owner
   * @param {Scheduler.view.Scheduler} source The grid
   * @param {Core.widget.Menu} menu The menu
   * @param {Object<String,MenuItemConfig>} items Menu item configs
   * @param {Scheduler.model.ResourceModel} record Record
   */
  /**
   * This event fires on the owning scheduler when an item is selected in the context menu.
   * @event resourceMenuItem
   * @on-owner
   * @param {Scheduler.view.Scheduler} source The grid
   * @param {Core.widget.Menu} menu The menu
   * @param {Core.widget.MenuItem} item Selected menu item
   * @param {Scheduler.model.ResourceModel} record Record
   */
  /**
   * This event fires on the owning grid when a check item is toggled in the context menu.
   * @event resourceMenuToggleItem
   * @on-owner
   * @param {Scheduler.view.Scheduler} source The grid
   * @param {Core.widget.Menu} menu The menu
   * @param {Core.widget.MenuItem} item Selected menu item
   * @param {Scheduler.model.ResourceModel} resourceRecord Record
   * @param {Boolean} checked Checked or not
   */
  //endregion
  //region Menu handlers
  getDataFromEvent(event) {
    const result = super.getDataFromEvent(...arguments), cellElement = result.cellElement = event.target.closest(".b-resourceheader-cell"), resourceId = cellElement == null ? void 0 : cellElement.dataset.resourceId;
    if (resourceId) {
      result.resourceRecord = this.client.resourceStore.getById(resourceId);
      return result;
    }
  }
  //endregion
  //region Getters/Setters
  populateResourceMenu({ items: items2, resourceRecord }) {
    const { client } = this;
    if (!client.readOnly) {
      items2.remove = {
        text: "L{CellMenu.removeRow}",
        localeClass: this,
        icon: "b-fw-icon b-icon-trash",
        cls: "b-separator",
        weight: 100,
        disabled: resourceRecord.readOnly,
        onItem: () => client.resourceStore.remove(resourceRecord)
      };
    }
  }
  get showMenu() {
    return true;
  }
  //endregion
};
//region Config
__publicField(ResourceMenu, "$name", "ResourceMenu");
__publicField(ResourceMenu, "configurable", {
  /**
   * A function called before displaying the menu that allows manipulations of its items.
   * Returning `false` from this function prevents the menu being shown.
   *
   * ```javascript
   * features : {
   *     resourceMenu : {
   *         processItems({ items, record, column }) {
   *             // Add or hide existing items here as needed
   *             items.myAction = {
   *                 text   : 'Cool action',
   *                 icon   : 'b-fa b-fa-fw b-fa-ban',
   *                 onItem : () => console.log(`Clicked ${record.name}`),
   *                 weight : 1000 // Move to end
   *             };
   *
   *             if (!record.allowDelete) {
   *                 items.remove.hidden = true;
   *             }
   *         }
   *     }
   * },
   * ```
   *
   * @config {Function}
   * @param {Object} context An object with information about the menu being shown.
   * @param {Scheduler.feature.ResourceMenu} context.feature A reference to this feature.
   * @param {Event} context.domEvent The initiating event.
   * @param {Event} context.event DEPRECATED: The initiating event.
   * @param {Number[]} context.point The client `X` and `Y` position of the initiating event.
   * @param {HTMLElement} context.targetElement The target to which the menu is being applied.
   * @param {Scheduler.model.ResourceModel} context.resourceRecord The record representing the current resource.
   * @param {Object<String,MenuItemConfig|Boolean>} context.items An object containing the
   *   {@link Core.widget.MenuItem menu item} configs keyed by their id.
   * @returns {Boolean|null} Returning `false` from this function prevents the menu being shown
   *
   * @preventable
   */
  processItems: null,
  /**
   * {@link Core.widget.Menu} items object containing named child menu items to apply to the feature's
   * provided context menu.
   *
   * This may add extra items as below, but you can also configure, or remove any of the default items by
   * configuring the name of the item as `null`
   *
   * ```javascript
   * features : {
   *     resourceMenu : {
   *         // This object is applied to the Feature's predefined default items
   *         items : {
   *             switchToDog : {
   *                 text : 'Dog',
   *                 icon : 'b-fa b-fa-fw b-fa-dog',
   *                 onItem({record}) {
   *                     record.dog = true;
   *                     record.cat = false;
   *                 },
   *                 weight : 500     // Make this second from end
   *             },
   *             switchToCat : {
   *                 text : 'Cat',
   *                 icon : 'b-fa b-fa-fw b-fa-cat',
   *                 onItem({record}) {
   *                     record.dog = false;
   *                     record.cat = true;
   *                 },
   *                 weight : 510     // Make this sink to end
   *             },
   *             remove : {
   *                 // Change icon for the delete item
   *                 icon : 'b-fa b-fa-times'
   *             }
   *         }
   *     }
   * },
   * ```
   *
   * @config {Object<String,MenuItemConfig|Boolean|null>}
   */
  items: null,
  type: "resource"
});
ResourceMenu.featureClass = "";
ResourceMenu._$name = "ResourceMenu";
GridFeatureManager.registerFeature(ResourceMenu, false, ["Scheduler"]);

// ../Scheduler/lib/Scheduler/feature/ResourceTimeRanges.js
var ResourceTimeRanges = class extends ResourceTimeRangesBase {
  static get pluginConfig() {
    const superConfig = super.pluginConfig;
    return {
      ...superConfig,
      assign: ["resolveResourceTimeRangeRecord", "getElementFromResourceTimeRangeRecord"]
    };
  }
  //endregion
  //region Init
  attachToProject(project) {
    const me = this, { client: scheduler } = me;
    super.attachToProject(project);
    if (!project.resourceTimeRangeStore) {
      project.resourceTimeRangeStore = scheduler.resourceTimeRangeStore || new ResourceTimeRangeStore({
        owner: me
      });
      const { crudManager } = scheduler;
      if (crudManager && !crudManager.resourceTimeRangeStore) {
        crudManager.resourceTimeRangeStore = project.resourceTimeRangeStore;
      }
    }
    const store = project.resourceTimeRangeStore;
    if (!me.exposedOnScheduler) {
      if (scheduler.resourceTimeRanges) {
        store.add(scheduler.resourceTimeRanges);
        delete scheduler.resourceTimeRanges;
      }
      Object.defineProperty(scheduler, "resourceTimeRanges", {
        get: () => store.records,
        set: (resourceTimeRanges) => store.data = resourceTimeRanges
      });
      me.exposedOnScheduler = true;
    }
    if (!store.resourceStore) {
      store.resourceStore = project.resourceStore;
    }
    me.detachListeners("store");
    store.ion({
      name: "store",
      change: me.onStoreChange,
      refresh: me.onStoreChange,
      thisObj: me
    });
  }
  // Called by ProjectConsumer after a new store is assigned at runtime
  attachToResourceTimeRangeStore(store) {
    this.attachToProject(this.project);
    this.client.refresh();
  }
  get store() {
    return this.project.resourceTimeRangeStore;
  }
  //endregion
  //region Events
  /**
   * Triggered for mouse down ona resource time range. Only triggered if the ResourceTimeRange feature is configured
   * with `enableMouseEvents: true`.
   * @event resourceTimeRangeMouseDown
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.feature.ResourceTimeRanges} feature The ResourceTimeRange feature
   * @param {Scheduler.model.ResourceTimeRangeModel} resourceTimeRangeRecord Resource time range record
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {MouseEvent} domEvent Browser event
   * @on-owner
   */
  /**
   * Triggered for mouse up ona resource time range. Only triggered if the ResourceTimeRange feature is configured
   * with `enableMouseEvents: true`.
   * @event resourceTimeRangeMouseUp
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.feature.ResourceTimeRanges} feature The ResourceTimeRange feature
   * @param {Scheduler.model.ResourceTimeRangeModel} resourceTimeRangeRecord Resource time range record
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {MouseEvent} domEvent Browser event
   * @on-owner
   */
  /**
   * Triggered for click on a resource time range. Only triggered if the ResourceTimeRange feature is configured with
   * `enableMouseEvents: true`.
   * @event resourceTimeRangeClick
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.feature.ResourceTimeRanges} feature The ResourceTimeRange feature
   * @param {Scheduler.model.ResourceTimeRangeModel} resourceTimeRangeRecord Resource time range record
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {MouseEvent} domEvent Browser event
   * @on-owner
   */
  /**
   * Triggered for double-click on a resource time range. Only triggered if the ResourceTimeRange feature is configured
   * with `enableMouseEvents: true`.
   * @event resourceTimeRangeDblClick
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.feature.ResourceTimeRanges} feature The ResourceTimeRange feature
   * @param {Scheduler.model.ResourceTimeRangeModel} resourceTimeRangeRecord Resource time range record
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {MouseEvent} domEvent Browser event
   * @on-owner
   */
  /**
   * Triggered for right-click on a resource time range. Only triggered if the ResourceTimeRange feature is configured
   * with `enableMouseEvents: true`.
   * @event resourceTimeRangeContextMenu
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.feature.ResourceTimeRanges} feature The ResourceTimeRange feature
   * @param {Scheduler.model.ResourceTimeRangeModel} resourceTimeRangeRecord Resource time range record
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {MouseEvent} domEvent Browser event
   * @on-owner
   */
  /**
   * Triggered for mouse over on a resource time range. Only triggered if the ResourceTimeRange feature is configured
   * with `enableMouseEvents: true`.
   * @event resourceTimeRangeMouseOver
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.feature.ResourceTimeRanges} feature The ResourceTimeRange feature
   * @param {Scheduler.model.ResourceTimeRangeModel} resourceTimeRangeRecord Resource time range record
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {MouseEvent} domEvent Browser event
   * @on-owner
   */
  /**
   * Triggered for mouse out of a resource time range. Only triggered if the ResourceTimeRange feature is configured
   * with `enableMouseEvents: true`.
   * @event resourceTimeRangeMouseOut
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.feature.ResourceTimeRanges} feature The ResourceTimeRange feature
   * @param {Scheduler.model.ResourceTimeRangeModel} resourceTimeRangeRecord Resource time range record
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {MouseEvent} domEvent Browser event
   * @on-owner
   */
  //endregion
  /**
   * Returns a resource time range record from the passed element
   * @param {HTMLElement} rangeElement
   * @returns {Scheduler.model.ResourceTimeRangeModel}
   * @on-owner
   * @function resolveResourceTimeRangeRecord
   * @category DOM
   */
  /**
   * Returns the element for the passed resource time range record, if rendered into DOM.
   * @param {Scheduler.model.ResourceTimeRangeModel} record
   * @returns {HTMLElement}
   * @on-owner
   * @function getElementFromResourceTimeRangeRecord
   * @category DOM
   */
  // Called on render of resources events to get events to render. Add any ranges
  // (chained function from Scheduler)
  getEventsToRender(resourceRecord, events) {
    var _a5;
    if (!this.disabled) {
      const { timeRanges } = resourceRecord.$original, { startDate, endDate, visibleDateRange } = this.client;
      (_a5 = this.store.lazyLoad) == null ? void 0 : _a5.bufferedLazyLoad({
        startDate,
        endDate,
        resourceRecord,
        visibleStart: visibleDateRange.startDate,
        visibleEnd: visibleDateRange.endDate
      });
      if (timeRanges == null ? void 0 : timeRanges.length) {
        timeRanges.forEach((timeRange) => {
          if (timeRange.isRecurring) {
            events.push(...timeRange.getOccurrencesForDateRange(startDate, endDate));
          } else {
            events.push(timeRange);
          }
        });
      }
    }
    return events;
  }
  /**
   * An empty function by default, but provided so that you can override it. This function is called each time
   * a resource time range is rendered into the schedule. It's called with `resourceTimeRangeRecord`, `resourceRecord`,
   * and `renderData` params.
   *
   * **IMPORTANT:** You should never modify any data on the ResourceTimeRangeModel inside this method.
   *
   * By default, the DOM markup of a resource time range bar includes placeholders for 'cls' and 'style'. The cls
   * property is a {@link Core.helper.util.DomClassList} which will be added to the main element. The style property
   * is an inline style declaration for the main element.
   *
   * **IMPORTANT:** When returning content, be sure to consider how that content should be encoded to avoid XSS
   * (Cross-Site Scripting) attacks. This is especially important when including user-controlled data such as
   * the event's `name`. The function {@link Core.helper.StringHelper#function-encodeHtml-static} as well as
   * {@link Core.helper.StringHelper#function-xss-static} can be helpful in these cases.
   *
   * ```javascript
   * resourceTimeRangeRenderer({ resourceTimeRangeRecord, resourceRecord, renderData }) {
   *     renderData.style = 'color:white'; // You can use inline styles too.
   *
   *     // Property names with truthy values are added to the resulting elements CSS class.
   *     renderData.cls.isModified = resourceTimeRangeRecord.isModified;
   *
   *     // Or, you can treat it as a string, but this is less efficient, especially
   *     // if your renderer wants to *remove* classes that may be there.
   *     renderData.cls += ' extra-class';
   *
   *     return StringHelper.xss`${DateHelper.format(resourceTimeRangeRecord.startDate, 'YYYY-MM-DD')}:
   *     ${resourceTimeRangeRecord.name}`;
   * }
   * ```
   *
   * @config {Function} resourceTimeRangeRenderer
   * @param {Object} detail An object containing the information needed to render a ResourceTimeRangeModel.
   * @param {Scheduler.model.ResourceTimeRangeModel} detail.resourceTimeRangeRecord The resource time range record.
   * @param {Scheduler.model.ResourceModel} detail.resourceRecord The resource record.
   * @param {Object} detail.renderData An object containing details about the event rendering.
   * @returns {String|DomConfig|DomConfig[]} A simple string, or a DomConfig object describing the markup
   * @on-owner
   */
  shouldInclude(eventRecord) {
    return eventRecord.isResourceTimeRange && !eventRecord.isNonWorking && !eventRecord.isCalendarHighlightModel;
  }
  renderContent(eventRecord, renderData) {
    const { client } = this, { eventContent } = renderData;
    if (client.resourceTimeRangeRenderer) {
      const value2 = client.resourceTimeRangeRenderer({
        resourceTimeRangeRecord: eventRecord,
        resourceRecord: renderData.resourceRecord,
        renderData
      });
      if (Array.isArray(value2)) {
        eventContent.children = value2;
      } else if (StringHelper.isHtml(value2)) {
        eventContent.children = null;
        eventContent.html = value2;
      } else if (typeof value2 === "string" || typeof value2 === "object") {
        eventContent.children = [value2];
      } else if (value2 != null) {
        eventContent.text = value2;
      }
    } else {
      super.renderContent(...arguments);
    }
  }
  doDestroy() {
    var _a5;
    if (((_a5 = this.store) == null ? void 0 : _a5.owner) === this) {
      this.store.destroy();
    }
    super.doDestroy();
  }
};
//region Config
__publicField(ResourceTimeRanges, "$name", "ResourceTimeRanges");
__publicField(ResourceTimeRanges, "configurable", {
  rangeCls: "b-sch-resourcetimerange",
  /**
   * Set to `true` to allow mouse interactions with the rendered range elements. By default, the range elements
   * are not reachable with the mouse, and only serve as a static background.
   * @prp {Boolean}
   * @default
   */
  enableMouseEvents: false,
  /**
   * Specify value to use for the tabIndex attribute of resource time range elements
   * @config {Number}
   * @default
   */
  tabIndex: 0,
  entityName: "resourceTimeRange"
});
ResourceTimeRanges.featureClass = "";
ResourceTimeRanges._$name = "ResourceTimeRanges";
GridFeatureManager.registerFeature(ResourceTimeRanges, false, "Scheduler");

// ../Scheduler/lib/Scheduler/feature/RowReorder.js
var RowReorder2 = class extends TransactionalFeature_default(RowReorder) {
  onDragStart(...args) {
    super.onDragStart(...args);
    if (this.client.transactionalFeaturesEnabled) {
      return this.startFeatureTransaction();
    }
  }
  onDrop(...args) {
    this.rejectFeatureTransaction();
    return super.onDrop(...args);
  }
  onAbort(...args) {
    this.rejectFeatureTransaction();
    return super.onAbort(...args);
  }
};
__publicField(RowReorder2, "$name", "RowReorder");
RowReorder2._$name = "RowReorder";
GridFeatureManager.registerFeature(RowReorder2, false, "Scheduler");
GridFeatureManager.registerFeature(RowReorder2, true, "Gantt");

// ../Scheduler/lib/Scheduler/feature/RowResize.js
var RowResize2 = class extends RowResize {
  onResizeStart({ context }) {
    var _a5, _b2, _c2;
    super.onResizeStart(...arguments);
    const { client } = this, { record } = context;
    if (client.isTimelineBase) {
      client.suspendAnimations();
      (_b2 = (_a5 = client.crudManager) == null ? void 0 : _a5.suspendAutoSync) == null ? void 0 : _b2.call(_a5);
      if (client.features.eventDragCreate) {
        this._oldDragCreateDisabled = client.features.eventDragCreate.disabled;
        client.features.eventDragCreate.disabled = true;
      }
    }
    if (client.isSchedulerBase) {
      if (((_c2 = client.getEventLayout) == null ? void 0 : _c2.call(client, record).type) === "stack") {
        context.oldEventLayout = record.eventLayout;
        record.eventLayout = "pack";
        record.rowHeight = context.elementHeight;
      }
      context.originalBarMargin = client.getBarMargin(record);
      context.originalResourceMarginObject = client.getResourceMarginObject(record);
      context.originalRecordResourceMargin = record.resourceMargin;
    }
  }
  onResizing({ context }) {
    const { record, newHeight, elementHeight, originalBarMargin, originalResourceMarginObject } = context;
    super.onResizing(...arguments);
    if (!this.applyToAllRows && !this.client.fixedRowHeight && context.valid) {
      record.beginBatch();
      record.rowHeight = newHeight;
      if (newHeight < elementHeight) {
        const percentage = newHeight / elementHeight;
        record.barMargin = originalBarMargin * percentage;
        if (originalResourceMarginObject.start === originalResourceMarginObject.end) {
          record.resourceMargin = originalResourceMarginObject.start * percentage;
        }
      }
      record.endBatch();
    }
  }
  onCancel({ context }) {
    context.record.eventLayout = context.oldEventLayout;
    context.record.resourceMargin = context.originalRecordResourceMargin;
    context.record.barMargin = context.originalBarMargin;
    super.onCancel(...arguments);
  }
  cleanup() {
    var _a5, _b2;
    const { client } = this;
    if (client.isTimelineBase) {
      client.resumeAnimations();
      (_b2 = (_a5 = client.crudManager) == null ? void 0 : _a5.resumeAutoSync) == null ? void 0 : _b2.call(_a5);
      if (client.features.eventDragCreate) {
        client.features.eventDragCreate.disabled = this._oldDragCreateDisabled;
      }
    }
    super.cleanup();
  }
};
__publicField(RowResize2, "$name", "RowResize");
RowResize2._$name = "RowResize";
GridFeatureManager.registerFeature(RowResize2, false);

// ../Scheduler/lib/Scheduler/feature/ScheduleMenu.js
var ScheduleMenu = class extends TimeSpanMenuBase {
  static get defaultConfig() {
    return {
      type: "schedule",
      /**
       * This is a preconfigured set of items used to create the default context menu.
       *
       * The `items` provided by this feature are listed below. These are the predefined property names which you
       * may configure:
       *
       * - `addEvent` Add an event for at the resource and time indicated by the `contextmenu` event.
       *
       * To remove existing items, set corresponding keys `null`:
       *
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         scheduleMenu : {
       *             items : {
       *                 addEvent : null
       *             }
       *         }
       *     }
       * });
       * ```
       *
       * @config {Object<String,MenuItemConfig|Boolean|null>} items
       */
      items: null,
      /**
       * A function called before displaying the menu that allows manipulations of its items.
       * Returning `false` from this function prevents the menu being shown.
       *
       * ```javascript
       * features         : {
       *    scheduleMenu : {
       *         processItems({ items, date, resourceRecord }) {
       *            // Add or hide existing items here as needed
       *            items.myAction = {
       *                text   : 'Cool action',
       *                icon   : 'b-fa b-fa-cat',
       *                onItem : () => console.log(`Clicked on ${resourceRecord.name} at ${date}`),
       *                weight : 1000 // Move to end
       *            };
       *
       *            if (!resourceRecord.allowAdd) {
       *                items.addEvent.hidden = true;
       *            }
       *        }
       *    }
       * },
       * ```
       *
       * @config {Function}
       * @param {Object} context An object with information about the menu being shown
       * @param {Scheduler.feature.ScheduleMenu} context.feature A reference to this feature.
       * @param {Event} context.domEvent The initiating event.
       * @param {Event} context.event DEPRECATED: The initiating event.
       * @param {Number[]} context.point The client `X` and `Y` position of the initiating event.
       * @param {HTMLElement} context.targetElement The target to which the menu is being applied.
       * @param {Scheduler.model.ResourceModel} context.resourceRecord The record representing the current resource
       * @param {Date} context.date The clicked date
       * @param {Object<String,MenuItemConfig|Boolean>} context.items An object containing the
       *   {@link Core.widget.MenuItem menu item} configs keyed by their id.
       * @returns {Boolean|null} Returning `false` from this function prevents the menu being shown
       * @preventable
       */
      processItems: null
    };
  }
  static get pluginConfig() {
    const config = super.pluginConfig;
    config.chain.push("populateScheduleMenu");
    return config;
  }
  //endregion
  //region Events
  /**
   * This event fires on the owning Scheduler or Gantt widget before the context menu is shown for the schedule.
   * Allows manipulation of the items to show in the same way as in `processItems`. Returning `false` from a listener
   * prevents the menu from being shown.
   * @event scheduleMenuBeforeShow
   * @on-owner
   * @preventable
   * @param {Scheduler.view.Scheduler} source
   * @param {Object<String,MenuItemConfig>} items Menu item configs
   * @param {Scheduler.model.EventModel} eventRecord Event record for which the menu was triggered
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {Date} date Clicked date, rounded according to viewPreset's settings
   * @param {Scheduler.model.AssignmentModel} assignmentRecord Assignment record, if assignments are used
   * @param {HTMLElement} eventElement
   */
  /**
   * This event fires on the owning Scheduler or Gantt widget when an item is selected in the context menu.
   * @event scheduleMenuItem
   * @on-owner
   * @param {Scheduler.view.Scheduler} source
   * @param {Core.widget.MenuItem} item
   * @param {Scheduler.model.ResourceModel} resourceRecord
   * @param {Date} date Clicked date, rounded according to viewPreset's settings
   * @param {HTMLElement} element
   */
  /**
   * This event fires on the owning Scheduler or Gantt widget after showing the context menu for the schedule.
   * @event scheduleMenuShow
   * @on-owner
   * @param {Scheduler.view.Scheduler} source
   * @param {Object<String,MenuItemConfig>} items Menu item configs
   * @param {Core.widget.Menu} menu The menu
   * @param {Scheduler.model.ResourceModel} resourceRecord
   * @param {Date} date Clicked date, rounded according to viewPreset's settings
   * @param {HTMLElement} targetElement
   */
  //endregion
  shouldShowMenu(eventParams) {
    const { client } = this, {
      targetElement,
      resourceRecord
    } = eventParams, isTimeAxisColumn = client.timeAxisSubGridElement.contains(targetElement);
    return !targetElement.closest(client.eventSelector) && isTimeAxisColumn && !(resourceRecord && resourceRecord.isSpecialRow);
  }
  getDataFromEvent(event) {
    var _a5, _b2;
    if (DomHelper.isDOMEvent(event)) {
      if (event.target.closest(".b-verticaltimeaxis-cell")) {
        return;
      }
      const { client } = this, cellData = (_a5 = client.getCellDataFromEvent) == null ? void 0 : _a5.call(client, event), date2 = (_b2 = client.getDateFromDomEvent) == null ? void 0 : _b2.call(client, event, "floor"), resourceRecord = client.resolveResourceRecord(event) || client.isVertical && client.resourceStore.last;
      return ObjectHelper.assign(super.getDataFromEvent(event), cellData, { date: date2, resourceRecord });
    }
    return event;
  }
  populateScheduleMenu({ items: items2, resourceRecord, date: date2 }) {
    const { client } = this;
    if (!client.readOnly && client.eventStore) {
      items2.addEvent = {
        text: "L{SchedulerBase.Add event}",
        icon: "b-icon b-icon-add",
        disabled: this.disableAddEvent,
        weight: 100,
        onItem() {
          client.createEvent(date2, resourceRecord, client.getRowFor(resourceRecord));
        }
      };
    }
  }
  get disableAddEvent() {
    const { resourceRecord, date: date2 } = this.menuContext;
    return !resourceRecord || resourceRecord.readOnly || !resourceRecord.isWorkingTime(date2);
  }
};
//region Config
__publicField(ScheduleMenu, "$name", "ScheduleMenu");
ScheduleMenu.featureClass = "";
ScheduleMenu._$name = "ScheduleMenu";
GridFeatureManager.registerFeature(ScheduleMenu, true, "Scheduler");

// ../Scheduler/lib/Scheduler/feature/ScrollButtons.js
var ScrollButtons = class extends InstancePlugin {
  static get pluginConfig() {
    return {
      chain: [
        "onPaint",
        "onElementClick"
      ]
    };
  }
  //endregion
  doDisable(disable) {
    this.client.refresh();
    super.doDisable(disable);
  }
  onPaint({ firstPaint }) {
    if (firstPaint) {
      const { client, renderer } = this, { timeAxisColumn } = client, { defaultRenderer } = timeAxisColumn;
      client.timeAxisSubGrid.scrollable.ion({
        scrollEnd() {
          client.refreshColumn(timeAxisColumn);
        }
      });
      timeAxisColumn.defaultRenderer = (...args) => {
        defaultRenderer.apply(timeAxisColumn, args);
        return renderer.apply(this, args);
      };
    }
  }
  renderer({ record: resourceRecord, grid }) {
    const { visibleDateRange } = grid, { labelRenderer, forwardIconCls, backwardIconCls } = this;
    let nbrEventsAfter = 0, nbrEventsBefore = 0;
    resourceRecord.events.forEach(({ isScheduled, startDate, endDate }) => {
      if (!isScheduled) {
        return;
      }
      if (endDate <= visibleDateRange.startDate) {
        nbrEventsBefore++;
      } else if (startDate >= visibleDateRange.endDate) {
        nbrEventsAfter++;
      }
    });
    return {
      class: "b-scroll-buttons-container",
      children: [
        {
          // We always render this button but make it invisible if there are no events before so the positioning
          // of b-scroll-forward button remains same
          class: `b-scroll-button-wrap b-scroll-backward ${nbrEventsBefore > 0 ? "" : "b-scroll-button-hidden"}`,
          children: [
            {
              tag: "i",
              class: {
                "b-icon": 1,
                "b-scroll-button": 1,
                [backwardIconCls]: 1
              }
            },
            labelRenderer && {
              tag: "span",
              class: "b-scroll-button-content",
              text: labelRenderer({ resourceRecord, isBefore: true, nbrEvents: nbrEventsBefore })
            }
          ]
        },
        nbrEventsAfter > 0 ? {
          class: "b-scroll-button-wrap b-scroll-forward",
          children: [
            labelRenderer && {
              tag: "span",
              class: "b-scroll-button-content",
              text: labelRenderer({ resourceRecord, isBefore: false, nbrEvents: nbrEventsAfter })
            },
            {
              tag: "i",
              class: {
                "b-icon": 1,
                "b-scroll-button": 1,
                [forwardIconCls]: 1
              }
            }
          ]
        } : null
      ]
    };
  }
  onElementClick(domEvent) {
    const { client } = this, { target } = domEvent, isScrollButton = target.closest(".b-scroll-button-wrap"), resourceRecord = isScrollButton && client.getRecordFromElement(target);
    if (isScrollButton && client.trigger("scrollButtonClick", { domEvent, resourceRecord }) !== false) {
      const { visibleDateRange } = client, isForward = target.closest(".b-scroll-forward");
      if (isForward) {
        client.scrollResourceEventIntoView(resourceRecord, resourceRecord.getEventStartingAfter(visibleDateRange.endDate), ObjectHelper.assign({ block: "end" }, this.scrollOptions));
      } else {
        client.scrollResourceEventIntoView(resourceRecord, resourceRecord.getEventEndingBefore(visibleDateRange.startDate), ObjectHelper.assign({ block: "start" }, this.scrollOptions));
      }
    }
  }
};
//region Config
__publicField(ScrollButtons, "$name", "ScrollButtons");
// Default configuration.
__publicField(ScrollButtons, "configurable", {
  /**
   * The icon to use for the button scrolling into the past
   * @config {String}
   * @default
   */
  backwardIconCls: "b-icon-before",
  /**
   * The icon to use for the button scrolling into the future
   * @config {String}
   * @default
   */
  forwardIconCls: "b-icon-after",
  /**
   * A method letting you render a label inside the button.
   *
   * {@note}When returning content, be sure to consider how that content should be encoded to avoid XSS
   * (Cross-Site Scripting) attacks. This is especially important when including user-controlled data such as
   * the event's `name`. The function {@link Core.helper.StringHelper#function-encodeHtml-static} as well as
   * {@link Core.helper.StringHelper#function-xss-static} can be helpful in these cases.{/@note}
   *
   * @config {Function} labelRenderer
   * @param {Object} detail An object that contains data about the event being rendered.
   * @param {Scheduler.model.ResourceModel} detail.resourceRecord The resource record
   * @param {Boolean} detail.isBefore `true` if the label being rendered is for the arrow scrolling into the past
   * @param {Number} detail.nbrEvents The number of events that can be scrolled to
   * @returns {String} The text or HTML
   */
  labelRenderer: null,
  /**
   * A config object describing how the scroll action should be performed.
   * @config {BryntumScrollOptions}
   */
  scrollOptions: { animate: { duration: 600, easing: "easeTo" }, y: false, edgeOffset: 50 },
  testConfig: {
    scrollOptions: { animate: { duration: 20, easing: "easeTo" }, y: false, edgeOffset: 50 }
  }
});
ScrollButtons._$name = "ScrollButtons";
GridFeatureManager.registerFeature(ScrollButtons, false, "Scheduler");

// ../Scheduler/lib/Scheduler/feature/ScheduleTooltip.js
var ScheduleTooltip = class extends InstancePlugin {
  static get configurable() {
    return {
      messageTemplate: (data) => `<div class="b-sch-hovertip-msg">${data.message}</div>`,
      /**
       * Set to `true` to hide this tooltip when hovering non-working time. Defaults to `false` for Scheduler,
       * `true` for SchedulerPro
       * @config {Boolean}
       */
      hideForNonWorkingTime: null
    };
  }
  // Plugin configuration. This plugin chains some of the functions in Grid.
  static get pluginConfig() {
    return {
      chain: ["onInternalPaint"]
    };
  }
  //endregion
  //region Init
  /**
   * Set up drag and drop and hover tooltip.
   * @private
   */
  onInternalPaint({ firstPaint }) {
    if (!firstPaint) {
      return;
    }
    const me = this, { client } = me;
    if (client.isSchedulerPro && me.hideForNonWorkingTime === void 0) {
      me.hideForNonWorkingTime = true;
    }
    let reshowListener;
    const tip = me.hoverTip = new Tooltip({
      id: `${client.id}-schedule-tip`,
      cls: "b-sch-scheduletip",
      allowOver: true,
      hoverDelay: 0,
      hideDelay: 100,
      showOnHover: true,
      forElement: client.timeAxisSubGridElement,
      anchorToTarget: false,
      trackMouse: true,
      updateContentOnMouseMove: true,
      // disable text content and monitor resize for tooltip, otherwise it doesn't
      // get sized properly on first appearance
      monitorResize: false,
      textContent: false,
      forSelector: ".b-schedulerbase:not(.b-dragging-event,.b-dragcreating,.b-creating-dependency) .b-grid-body-container:not(.b-scrolling) .b-timeline-subgrid:not(.b-scrolling) > :not(.b-sch-foreground-canvas,.b-group-footer,.b-group-row) *:not(.b-scroll-button-wrap *,.b-sch-dependency)",
      // Do not constrain at all, want it to be able to go outside of the viewport to not get in the way
      getHtml: me.getHoverTipHtml.bind(me),
      onDocumentMouseDown(event) {
        if (tip.forElement.contains(event.event.target)) {
          reshowListener = EventHelper.on({
            thisObj: me,
            element: client.timeAxisSubGridElement,
            mousemove: (e) => tip.internalOnPointerOver(e),
            capture: true
          });
        }
        const hideAnimation = tip.hideAnimation;
        tip.hideAnimation = false;
        tip.constructor.prototype.onDocumentMouseDown.call(tip, event);
        tip.hideAnimation = hideAnimation;
      },
      // on Core/mixin/Events constructor, me.config.listeners is deleted and attributed its value to me.configuredListeners
      // to then on processConfiguredListeners it set me.listeners to our TooltipBase
      // but since we need our initial config.listeners to set to our internal tooltip, we leave processConfiguredListeners empty
      // to avoid lost our listeners to apply for our internal tooltip here and force our feature has all Tooltip events firing
      ...me.config,
      internalListeners: me.configuredListeners
    });
    tip.ion({
      pointerover({ event }) {
        const buttonsPressed = "buttons" in event ? event.buttons > 0 : event.which > 0;
        if (!buttonsPressed && reshowListener) {
          reshowListener();
        }
        if (me.disabled || buttonsPressed) {
          tip.hide();
          return false;
        }
        ;
      },
      innerhtmlupdate({ source }) {
        me.clockTemplate.updateDateIndicator(source.element, me.lastTime);
      }
    });
    client.ion({
      timeAxisViewModelUpdate: "updateTip",
      thisObj: me
    });
    me.clockTemplate = new ClockTemplate({
      scheduler: client
    });
  }
  // leave configuredListeners alone until render time at which they are used on the tooltip
  processConfiguredListeners() {
  }
  updateTip() {
    if (this.hoverTip.isVisible) {
      this.hoverTip.updateContent();
    }
  }
  doDestroy() {
    this.destroyProperties("clockTemplate", "hoverTip");
    super.doDestroy();
  }
  //endregion
  //region Contents
  /**
   * @deprecated Use {@link #function-generateTipContent} instead.
   * Gets html to display in hover tooltip (tooltip displayed on empty parts of scheduler)
   * @private
   */
  getHoverTipHtml({ tip, event }) {
    const me = this, scheduler = me.client, date2 = event && scheduler.getDateFromDomEvent(event, "floor", true);
    let html = me.lastHtml;
    if (date2 && event.target) {
      const resourceRecord = scheduler.resolveResourceRecord(event);
      if (resourceRecord && (date2 - me.lastTime !== 0 || resourceRecord.id !== me.lastResourceId)) {
        if (me.hideForNonWorkingTime) {
          const isWorkingTime2 = resourceRecord.isWorkingTime(date2);
          tip.element.classList.toggle("b-nonworking-time", !isWorkingTime2);
        }
        me.lastResourceId = resourceRecord.id;
        html = me.lastHtml = me.generateTipContent({ date: date2, event, resourceRecord });
      }
    } else {
      tip.hide();
      me.lastTime = null;
      me.lastResourceId = null;
    }
    return html;
  }
  /**
   * Called as mouse pointer is moved over a new resource or time block. You can override this to show
   * custom HTML in the tooltip.
   * @param {Object} context
   * @param {Date} context.date The date of the hovered point
   * @param {Event} context.event The DOM event that triggered this tooltip to show
   * @param {Scheduler.model.ResourceModel} context.resourceRecord The resource record
   * @returns {String} The HTML contents to show in the tooltip (an empty return value will hide the tooltip)
   */
  generateTipContent({ date: date2, event, resourceRecord }) {
    const me = this, clockHtml = me.clockTemplate.generateContent({
      date: date2,
      text: me.client.getFormattedDate(date2)
    }), messageHtml = me.messageTemplate({
      message: me.getText(date2, event, resourceRecord) || ""
    });
    me.lastTime = date2;
    return clockHtml + messageHtml;
  }
  /**
   * Override this to render custom text to default hover tip
   * @param {Date} date
   * @param {Event} event Browser event
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource record
   * @returns {String}
   */
  getText(date2, event, resourceRecord) {
  }
  //endregion
};
//region Config
__publicField(ScheduleTooltip, "$name", "ScheduleTooltip");
ScheduleTooltip.featureClass = "b-scheduletip";
ScheduleTooltip._$name = "ScheduleTooltip";
GridFeatureManager.registerFeature(ScheduleTooltip, true, "Scheduler");
GridFeatureManager.registerFeature(ScheduleTooltip, false, "ResourceUtilization");

// ../Scheduler/lib/Scheduler/feature/SimpleEventEdit.js
var SimpleEventEdit = class extends InstancePlugin.mixin(TaskEditStm_default) {
  static get defaultConfig() {
    return {
      /**
       * The event that shall trigger showing the editor. Defaults to `eventdblclick`, set to `''` or null to
       * disable editing of existing events.
       * @config {String}
       * @default
       * @category Editor
       */
      triggerEvent: "eventdblclick",
      /**
       * The current {@link Scheduler.model.EventModel} record, which is being edited by the event editor.
       * @property {Scheduler.model.EventModel}
       * @readonly
       */
      eventRecord: null,
      /**
       * The {@link Scheduler.model.EventModel} field to edit
       * @config {String}
       * @category Editor
       */
      field: "name",
      /**
       * The editor configuration, where you can control which widget to show
       * @config {EditorConfig}
       * @category Editor
       */
      editorConfig: null
    };
  }
  static get pluginConfig() {
    return {
      chain: ["onEventEnterKey", "editEvent"]
    };
  }
  //endregion
  //region Editing
  construct(scheduler, config) {
    const me = this;
    me.scheduler = scheduler;
    scheduler.eventEdit = me;
    super.construct(scheduler, config);
    me.clientListenersDetacher = scheduler.ion({
      [me.triggerEvent]: ({
        eventRecord,
        eventElement
      }) => me.editEvent(eventRecord, eventRecord.resource, eventElement),
      dragcreateend: me.onDragCreateEnd,
      thisObj: me
    });
  }
  doDestroy() {
    var _a5;
    this.clientListenersDetacher();
    (_a5 = this.editor) == null ? void 0 : _a5.destroy();
    super.doDestroy();
  }
  get eventStore() {
    return this.scheduler.eventStore;
  }
  get project() {
    return this.client.project;
  }
  // region Editor
  /**
   * Opens an Editor for the passed event. This function is exposed on Scheduler and can be called as
   * `scheduler.editEvent()`.
   * @param {Scheduler.model.EventModel} eventRecord The Event to edit
   * @param {Scheduler.model.ResourceModel} [resourceRecord] The Resource record for the event.
   * @on-owner
   * @async
   */
  async editEvent(eventRecord, resourceRecord, element, stmCapture) {
    const me = this, { scheduler } = me, { eventEdit } = me.client.features;
    if (!resourceRecord) {
      resourceRecord = eventRecord.resource;
    }
    if (!element) {
      element = scheduler.getElementFromEventRecord(eventRecord);
    }
    if (scheduler.readOnly || me.disabled || eventRecord.readOnly || resourceRecord.readOnly || eventEdit && !eventEdit.disabled && !eventRecord.isCreating) {
      return;
    }
    let { editor } = me;
    element = DomHelper.down(element, scheduler.eventInnerSelector) || element;
    eventRecord = eventRecord.isAssignment ? eventRecord.event : eventRecord;
    me.resource = resourceRecord;
    me.eventRecord = eventRecord;
    me.element = element;
    scheduler.element.classList.add("b-eventeditor-editing");
    if (editor) {
      editor.render(scheduler.timeAxisSubGridElement);
    } else {
      me.editor = editor = Editor.new({
        owner: scheduler.timeAxisSubGrid,
        appendTo: scheduler.timeAxisSubGridElement,
        scrollAction: "realign",
        maxHeight: 40,
        align: {
          align: scheduler.isHorizontal ? "c-c" : "t-t"
        },
        cls: "b-simpleeventeditor",
        internalListeners: {
          complete: "onEditorComplete",
          cancel: "onEditorCancel",
          thisObj: me
        },
        // Keys must not propagate into the scheduler
        onInternalKeyDown: (keyEvent) => keyEvent.stopPropagation()
      }, me.editorConfig);
      editor.revertFocus = () => scheduler.getElementFromEventRecord(me.eventRecord, me.resourceRecord, true).focus({ scrollIntoView: false });
      me.relayEvents(me.editor, ["beforestart", "start", "beforecomplete", "complete", "beforecancel", "cancel"]);
    }
    if (stmCapture) {
      me.applyStmCapture(stmCapture);
    } else if (stmCapture !== false && !me.hasStmCapture) {
      me.captureStm(true);
    }
    if (eventRecord.isCreating) {
      eventRecord.name = "";
    }
    await editor.startEdit({
      target: element,
      record: eventRecord,
      field: me.field
    });
    if (scheduler.isVertical && eventRecord.startDate < scheduler.visibleDateRange.startDate) {
      editor.element.scrollIntoView();
    }
    scheduler.navigator.disabled = true;
  }
  onEditorComplete() {
    this.eventRecord.isCreating = false;
    this.reset();
    this.freeStm(true);
  }
  onEditorCancel() {
    const { eventRecord } = this;
    this.reset();
    this.freeStm(false);
    if (eventRecord.isCreating) {
      eventRecord.remove();
    }
  }
  reset() {
    this.scheduler.element.classList.remove("b-eventeditor-editing");
    this.scheduler.navigator.disabled = false;
    this.eventRecord = null;
    this.resource = null;
  }
  //endregion
  // chained from EventNavigation
  onEventEnterKey({ assignmentRecord, eventRecord }) {
    const element = assignmentRecord ? this.scheduler.getElementFromAssignmentRecord(assignmentRecord) : this.scheduler.getElementFromEventRecord(eventRecord), resourceRecord = (assignmentRecord || eventRecord).resource;
    this.editEvent(eventRecord, resourceRecord, element);
  }
  //endregion
  onDragCreateEnd({ eventRecord, resourceRecord, eventElement, stmCapture }) {
    this.element = eventElement;
    this.editEvent(eventRecord, resourceRecord, eventElement, stmCapture);
  }
};
// region Events
/**
 * Fired before the editor is shown to start an edit operation. Returning `false` from a handler vetoes the edit operation.
 * @event beforeStart
 * @param {Object} value The value to be edited.
 * @param {Core.widget.Editor} source The Editor that triggered the event.
 * @preventable
 */
/**
 * Fired when an edit operation has begun.
 * @event start
 * @param {Object} value The starting value of the field.
 * @param {Core.widget.Editor} source The Editor that triggered the event.
 */
/**
 * Fired when an edit completion has been requested, either by `ENTER`, or focus loss (if configured to complete on blur).
 * The completion may be vetoed, in which case, focus is moved back into the editor.
 * @event beforeComplete
 * @param {Object} oldValue The original value.
 * @param {Object} value The new value.
 * @param {Core.widget.Editor} source The Editor that triggered the event.
 * @preventable
 */
/**
 * Edit has been completed, and any associated record or element has been updated.
 * @event complete
 * @param {Object} oldValue The original value.
 * @param {Object} value The new value.
 * @param {Core.widget.Editor} source The Editor that triggered the event.
 */
/**
 * Fired when cancellation has been requested, either by `ESC`, or focus loss (if configured to cancel on blur).
 * The cancellation may be vetoed, in which case, focus is moved back into the editor.
 * @event beforeCancel
 * @param {Object} oldValue The original value.
 * @param {Object} value The new value.
 * @param {Core.widget.Editor} source The Editor that triggered the event.
 * @preventable
 */
/**
 * Edit has been canceled without updating the associated record or element.
 * @event cancel
 * @param {Object} oldValue The original value.
 * @param {Object} value The value of the field.
 * @param {Core.widget.Editor} source The Editor that triggered the event.
 */
// endregion
//region Config
__publicField(SimpleEventEdit, "$name", "SimpleEventEdit");
SimpleEventEdit._$name = "SimpleEventEdit";
GridFeatureManager.registerFeature(SimpleEventEdit, false, "Scheduler");

// ../Scheduler/lib/Scheduler/feature/Split.js
var startScrollOptions2 = Object.freeze({
  animate: false,
  block: "start"
});
var endScrollOptions2 = Object.freeze({
  animate: false,
  block: "end"
});
var Split2 = class extends Split {
  static get pluginConfig() {
    const result = super.pluginConfig;
    result.chain.push("populateScheduleMenu");
    return result;
  }
  getClientConfig(appendTo, order, options, config) {
    var _a5;
    const { client } = this, clientConfig = super.getClientConfig(appendTo, order, options, config);
    if (!client.isSchedulerBase && !client.isSchedulerProBase && !client.isGanttBase) {
      return clientConfig;
    }
    delete clientConfig.store;
    delete clientConfig.events;
    delete clientConfig.resources;
    delete clientConfig.assignments;
    delete clientConfig.dependencies;
    delete clientConfig.resourceTimeRanges;
    delete clientConfig.timeRanges;
    clientConfig.project = client.project;
    clientConfig.assignmentStore = clientConfig.project.assignmentStore;
    clientConfig.crudManager = client.crudManager;
    clientConfig.viewPreset = client.viewPreset;
    clientConfig.startDate = client.startDate;
    clientConfig.endDate = client.endDate;
    clientConfig.selectedCollection = client.selectedCollection;
    clientConfig.useInitialAnimation = false;
    if (((_a5 = clientConfig.features) == null ? void 0 : _a5.eventDrag) !== false) {
      ObjectHelper.setPath(clientConfig, "features.eventDrag.constrainDragToTimeline", false);
      client.features.eventDrag.constrainDragToTimeline = false;
    }
    return clientConfig;
  }
  unsplitCleanup(options) {
    this.client.timeAxisSubGrid.onElementResize();
  }
  /**
   * Fired when splitting the Scheduler.
   * @event split
   * @param {Scheduler.view.SchedulerBase[]} subViews The sub views created by the split
   * @param {Object} options The options passed to the split call
   * @param {'horizontal'|'vertical'|'both'} options.direction The direction of the split
   * @param {Grid.column.Column} options.atColumn The column to split at
   * @param {Core.data.Model} options.atRecord The record to split at
   * @param {Date} options.atDate The date to split at
   * @on-owner
   */
  processOptions(options) {
    const { client } = this;
    let { atDate, atRecord } = options;
    if (options.direction !== "horizontal" && !options.atColumn && !options.atRecord && !atDate) {
      const bounds = Rectangle.from(client.element), date2 = client.getDateFromCoordinate(bounds.center.x, "round", false);
      if (date2) {
        options.atDate = atDate = date2;
        options.atColumn = client.timeAxisColumn;
      }
    }
    if (atDate) {
      if (!options.direction) {
        options.direction = atRecord ? "both" : "vertical";
      }
      options.splitX = client.getVirtualCoordFromDate(atDate) - client.timeAxisSubGrid.scrollable.x;
      if (client.rtl) {
        options.splitX += client._bodyRectangle.width - Rectangle.from(client.timeAxisSubGridElement).right + DomHelper.scrollBarWidth;
      } else {
        options.splitX += Rectangle.from(client.timeAxisSubGridElement, client.bodyContainer).left;
      }
      options.remainingWidth = Rectangle.from(client.element).width - options.splitX;
    }
    return super.processOptions(options);
  }
  /**
   * Split the scheduler into two or four parts.
   *
   * - Splits into two when passed `direction : 'vertical'`, `direction : 'horizontal'` or `atColumn`, `atRecord` or
   *   `atDate`.
   * - Splits into four when passed `direction : 'both'` or `atColumn`/`atDate` and `atRecord`.
   *
   * ```javascript
   * // Split horizontally (at the row in the center of the scheduler)
   * await scheduler.split({ direction : 'horizontal' });
   *
   * // Split both ways by a specific date and resource
   * await schedule.split({
   *    atRecord : scheduler.resourceStore.getById(10),
   *    atDate   : new Date(2023, 5, 8)
   * });
   * ```
   *
   * To return to a single scheduler, call {@link #function-unsplit}.
   *
   * Note that this function is callable directly on the scheduler instance.
   *
   * @param {Object} [options] Split options
   * @param {'vertical'|'horizontal'|'both'} [options.direction] Split direction, 'vertical', 'horizontal' or 'both'.
   * Not needed when passing `atColumn` or `atRecord`.
   * @param {Date} [options.atDate] Date to split at. Has to be within the time axis
   * @param {Grid.column.Column} [options.atColumn] Column to split at
   * @param {Core.data.Model} [options.atRecord] Record to split at
   * @returns {Promise} Resolves when split is complete, and subviews are scrolled to the correct position.
   * @async
   * @on-owner
   */
  async split(options = {}) {
    const { client } = this;
    this.detachListeners("onReconfigure");
    if (client.isVertical) {
      throw new Error("Splitting is not supported in vertical mode");
    }
    const result = await super.split(options), reconfigureTimeAxis = (firstTimeAxis, secondTimeAxis) => {
      firstTimeAxis.reconfigure({
        viewPreset: secondTimeAxis.viewPreset,
        startDate: secondTimeAxis.startDate,
        endDate: secondTimeAxis.endDate
      });
    };
    if (result) {
      if (options.atDate) {
        await Promise.all([
          // Scroll prev tick into view on left side
          result[0].scrollToDate(DateHelper.add(options.atDate, -1, client.timeAxis.unit), endScrollOptions2),
          // And current tick into view on right side
          result[1].scrollToDate(options.atDate, startScrollOptions2)
        ]);
      }
      if (client.infiniteScroll) {
        if (options.direction === "horizontal") {
          reconfigureTimeAxis(result[1].timeAxis, result[0].timeAxis);
        }
        if (options.direction === "both") {
          reconfigureTimeAxis(result[2].timeAxis, result[0].timeAxis);
          reconfigureTimeAxis(result[3].timeAxis, result[1].timeAxis);
        }
      }
    }
    client.timeAxisSubGrid.scrollable.x += 0.5;
    result.forEach((sc) => {
      sc.timeAxis.ion({
        name: "onReconfigure",
        reconfigure: "onReconfigure",
        thisObj: this
      });
    });
    return result;
  }
  //region Context menu
  // Overwritten to prevent splitting in locked region
  populateCellMenu() {
  }
  populateScheduleMenu({ items: items2, date: date2, record }) {
    const me = this, { isSplit } = me, { splitFrom } = me.client;
    if (!me.disabled && me.client.isHorizontal) {
      items2.splitSchedule = {
        text: "L{split}",
        localeClass: me,
        icon: "b-icon b-icon-split-vertical",
        weight: 500,
        separator: true,
        hidden: isSplit || splitFrom,
        menu: {
          splitHorizontally: {
            text: "L{horizontally}",
            icon: "b-icon b-icon-split-horizontal",
            localeClass: me,
            weight: 100,
            onItem() {
              me.split({ atRecord: record, direction: "horizontal" }).then();
            }
          },
          splitVertically: {
            text: "L{vertically}",
            icon: "b-icon b-icon-split-vertical",
            localeClass: me,
            weight: 200,
            onItem() {
              me.split({ atDate: date2 }).then();
            }
          },
          splitBoth: {
            text: "L{both}",
            icon: "b-icon b-icon-split-both",
            localeClass: me,
            weight: 300,
            onItem() {
              me.split({ atDate: date2, atRecord: record, direction: "both" }).then();
            }
          }
        }
      };
      items2.unsplitSchedule = {
        text: "L{unsplit}",
        localeClass: me,
        icon: "b-icon b-icon-clear",
        hidden: !(isSplit || splitFrom),
        weight: 550,
        onItem() {
          (splitFrom || me).unsplit();
        }
      };
    }
  }
  onReconfigure({ suppressRefresh, config }) {
    var _a5;
    const { client } = this;
    if (config && !this.isReconfiguring) {
      this.isReconfiguring = true;
      const splitFrom = client.splitFrom ? client.splitFrom : client;
      if (splitFrom.splitDirection && splitFrom.splitDirection !== "vertical") {
        (_a5 = client.syncSplits) == null ? void 0 : _a5.call(client, (split2) => {
          var _a6, _b2;
          const { subViews } = splitFrom, indexFrom = subViews.indexOf(split2), indexTo = subViews.length > 2 ? indexFrom + (indexFrom > 1 ? -1 : 1) * 2 : 1 - indexFrom;
          (_a6 = subViews[indexFrom]) == null ? void 0 : _a6.timeAxis.reconfigure(config, suppressRefresh, false);
          (_b2 = subViews[indexTo]) == null ? void 0 : _b2.timeAxis.reconfigure(config, suppressRefresh, false);
        });
      }
      this.isReconfiguring = false;
    }
  }
  //endregion
};
__publicField(Split2, "$name", "Split");
__publicField(Split2, "configurable", {
  /**
   * Properties whose changes should be relayed to sub-views at runtime.
   *
   * Supply an object with property names as keys, and a truthy value to relay the change, or a falsy value to not
   * relay it. The object will be merged with the default values.
   *
   * In addition to the properties relayed by Grid, Scheduler also relays these:
   * * {@link Scheduler/view/Scheduler#property-barMargin}
   * * {@link Scheduler/view/Scheduler#property-eventColor}
   * * {@link Scheduler/view/Scheduler#property-eventStyle}
   * * {@link Scheduler/view/Scheduler#property-eventLayout}
   * * {@link Scheduler/view/Scheduler#property-fillTicks}
   * * {@link Scheduler/view/Scheduler#property-resourceMargin}
   * * {@link Scheduler/view/Scheduler#property-snap}
   * * {@link Scheduler/view/Scheduler#property-tickSize}
   *
   * Example of supplying a custom set of properties to relay:
   * ```javascript
   * const scheduler = new Scheduler({
   *     features : {
   *         split : {
   *             relayProperties : {
   *                 barMargin : false, // Do not relay barMargin changes
   *                 myConfig : true   // Relay changes to the myConfig property
   *             }
   *         }
   *     }
   * }
   * ```
   * @config {Object<String,Boolean>}
   */
  relayProperties: {
    value: {
      barMargin: 1,
      eventColor: 1,
      eventStyle: 1,
      eventLayout: 1,
      fillTicks: 1,
      resourceMargin: 1,
      snap: 1,
      tickSize: 1
    },
    $config: {
      merge: "merge"
    }
  }
});
Split2._$name = "Split";
GridFeatureManager.registerFeature(Split2, false, "Scheduler");

// ../Scheduler/lib/Scheduler/feature/StickyEvents.js
var zeroMargins = { width: 0, height: 0 };
var StickyEvents = class extends InstancePlugin {
  static get pluginConfig() {
    return {
      chain: ["onEventDataGenerated"]
    };
  }
  construct(scheduler, config) {
    super.construct(scheduler, config);
    if (scheduler.isVertical) {
      this.toUpdate = /* @__PURE__ */ new Set();
      scheduler.ion({
        scroll: "onSchedulerScroll",
        horizontalScroll: "onHorizontalScroll",
        thisObj: this,
        prio: 1e4
      });
    }
  }
  onEventDataGenerated(renderData) {
    if (this.client.isHorizontal) {
      renderData.wrapperCls["b-disable-sticky"] = renderData.eventRecord.stickyContents === false;
    } else {
      this.syncEventContentPosition(renderData, void 0, true);
      this.updateStyles();
    }
  }
  //region Vertical mode
  onSchedulerScroll() {
    if (!this.disabled) {
      this.verticalSyncAllEventsContentPosition(this.client);
    }
  }
  // Have to sync also on horizontal scroll, since we reuse elements and dom configs
  onHorizontalScroll({ subGrid }) {
    if (subGrid === this.client.timeAxisSubGrid) {
      this.verticalSyncAllEventsContentPosition(this.client);
    }
  }
  updateStyles() {
    for (const { contentEl, style } of this.toUpdate) {
      DomHelper.applyStyle(contentEl, style);
    }
    this.toUpdate.clear();
  }
  verticalSyncAllEventsContentPosition(scheduler) {
    const { resourceMap } = scheduler.currentOrientation;
    for (const eventsData of resourceMap.values()) {
      for (const { renderData, elementConfig } of Object.values(eventsData)) {
        const args = [renderData];
        if (elementConfig && renderData.eventRecord.isResourceTimeRange) {
          args.push(elementConfig.children[0]);
        }
        this.syncEventContentPosition.apply(this, args);
      }
    }
    this.toUpdate.size && this.updateStyles();
  }
  syncEventContentPosition(renderData, eventContent = renderData.eventContent, duringGeneration = false) {
    if (this.disabled || // Allow client disable stickiness for certain events
    renderData.eventRecord.stickyContents === false) {
      return;
    }
    const { client } = this, {
      eventRecord,
      resourceRecord,
      useEventBuffer,
      bufferAfterWidth,
      bufferBeforeWidth,
      top,
      height
    } = renderData, scrollPosition = client.scrollable.y, wrapperEl = duringGeneration ? null : client.getElementFromEventRecord(eventRecord, resourceRecord, true), contentEl = wrapperEl && DomSync.getChild(wrapperEl, "event.content"), meta = eventRecord.instanceMeta(client), style = typeof eventContent.style === "string" ? eventContent.style = DomHelper.parseStyle(eventContent.style) : eventContent.style || (eventContent.style = {});
    if (wrapperEl == null ? void 0 : wrapperEl.classList.contains("b-dragging")) {
      return;
    }
    let start = top, contentSize = height, end = start + contentSize;
    if (useEventBuffer) {
      start += bufferBeforeWidth;
      contentSize = contentSize - bufferBeforeWidth - bufferAfterWidth;
      end = start + contentSize;
    }
    if (start < scrollPosition && end >= scrollPosition && !eventRecord.isMilestone) {
      const contentWidth = contentEl == null ? void 0 : contentEl.offsetWidth, justify = (contentEl == null ? void 0 : contentEl.parentNode) && DomHelper.getStyleValue(contentEl.parentNode, "justifyContent"), c = justify === "center" ? (renderData.width - contentWidth) / 2 : 0, eventStart = start, eventEnd = eventStart + contentSize - 1;
      if ((!contentEl || contentWidth) && eventStart < scrollPosition && eventEnd >= scrollPosition) {
        const edgeSizes = this.getEventContentMargins(contentEl), maxOffset = contentEl ? contentSize - contentEl.offsetHeight - edgeSizes.height - c : Number.MAX_SAFE_INTEGER, offset = Math.min(scrollPosition - eventStart, maxOffset - 2);
        style.transform = offset > 0 ? `translateY(${offset}px)` : "";
        meta.stuck = true;
      } else {
        style.transform = "";
        meta.stuck = false;
      }
      if (contentEl) {
        this.toUpdate.add({
          contentEl,
          style
        });
      }
    } else if (contentEl && meta.stuck) {
      style.transform = "";
      meta.stuck = false;
      this.toUpdate.add({
        contentEl,
        style
      });
    }
  }
  // Only measure the margins of an event's contentEl once
  getEventContentMargins(contentEl) {
    if (contentEl == null ? void 0 : contentEl.classList.contains("b-sch-event-content")) {
      return DomHelper.getEdgeSize(contentEl, "margin");
    }
    return zeroMargins;
  }
  //endregion
  doDisable() {
    super.doDisable(...arguments);
    if (!this.isConfiguring) {
      this.client.refreshWithTransition();
    }
  }
};
__publicField(StickyEvents, "$name", "StickyEvents");
__publicField(StickyEvents, "type", "stickyEvents");
StickyEvents._$name = "StickyEvents";
GridFeatureManager.registerFeature(StickyEvents, true, "Scheduler");
GridFeatureManager.registerFeature(StickyEvents, false, "ResourceHistogram");

// ../Scheduler/lib/Scheduler/feature/LockRows.js
var LockRows2 = class extends LockRows {
  onInternalPaint({ firstPaint }) {
    const me = this, { project } = me.client, { resourceStore } = project;
    if (firstPaint) {
      if (me.refreshSuspended) {
        me.client.resumeRefresh();
      }
      if (resourceStore.count) {
        me.lockRows();
      } else {
        const unListen = project.ion({
          refresh: () => {
            if (resourceStore.count) {
              unListen();
              me.lockRows();
            }
          }
        });
      }
    }
  }
  getClientConfig(appendTo, order, options, config) {
    const clientConfig = this.superGetClientConfig(appendTo, order, options, config);
    clientConfig.partner = this.client;
    delete clientConfig.crudManager;
    return clientConfig;
  }
};
__publicField(LockRows2, "$name", "LockRows");
var myProto = LockRows2.prototype;
var superProto = Split2.prototype;
myProto.superGetClientConfig = superProto.getClientConfig;
LockRows2.configurable.relayProperties = Split2.configurable.relayProperties;
LockRows2._$name = "LockRows";
GridFeatureManager.registerFeature(LockRows2, false, "Scheduler");

// ../Scheduler/lib/Scheduler/feature/TimelineSummary.js
var TimelineSummary = class extends Summary {
  static get configurable() {
    return {
      /**
       * Show tooltip containing summary values and labels
       * @config {Boolean}
       * @default
       */
      showTooltip: true
    };
  }
  // Plugin configuration. This plugin chains some of the functions in Grid.
  static get pluginConfig() {
    return {
      chain: ["renderRows", "updateProject"]
    };
  }
  //endregion
  //region Init
  construct(client, config) {
    const me = this;
    super.construct(client, config);
    if (!me.summaries) {
      me.summaries = [{ renderer: me.renderer }];
    }
    if (client.isTimelineBase) {
      me.updateProject(client.project);
      client.ion({
        timeAxisViewModelUpdate: me.renderRows,
        thisObj: me
      });
    }
  }
  //endregion
  //region Render
  updateProject(project) {
    this.detachListeners("summaryProject");
    project.ion({
      name: "summaryProject",
      dataReady: "updateTimelineSummaries",
      thisObj: this
    });
  }
  renderRows() {
    var _a5;
    if (this.client.isHorizontal) {
      (_a5 = this.client.timeAxisSubGrid.footer.element.querySelector(".b-grid-footer")) == null ? void 0 : _a5.classList.add("b-sch-summarybar");
    }
    super.renderRows();
    if (!this.disabled) {
      this.render();
    }
  }
  get summaryBarElement() {
    return this.client.element.querySelector(".b-sch-summarybar");
  }
  render() {
    const me = this, { client: timeline } = me, sizeProp = timeline.isHorizontal ? "width" : "height", colCfg = timeline.timeAxisViewModel.columnConfig, summaryContainer = me.summaryBarElement;
    if (summaryContainer) {
      if (!me._tip && me.showTooltip && me.summaries.some((config) => config.label)) {
        me._tip = new Tooltip({
          id: `${timeline.id}-summary-tip`,
          cls: "b-timeaxis-summary-tip",
          hoverDelay: 0,
          hideDelay: 100,
          forElement: summaryContainer,
          anchorToTarget: true,
          trackMouse: false,
          forSelector: ".b-timeaxis-tick",
          getHtml: ({ activeTarget }) => activeTarget._tipHtml
        });
      }
      summaryContainer.innerHTML = colCfg[colCfg.length - 1].map((col) => `<div class="b-timeaxis-tick" style="${sizeProp}: ${col.width}px"></div>`).join("");
      me.updateTimelineSummaries();
    }
  }
  //endregion
  /**
   * Refreshes the summaries
   */
  refresh() {
    super.refresh();
    this.updateTimelineSummaries();
  }
  doDisable(disable) {
    var _a5;
    const { isConfiguring } = this.client;
    super.doDisable(disable);
    (_a5 = this.summaryColumn) == null ? void 0 : _a5.toggle(!disable);
    if (!isConfiguring && !disable) {
      this.render();
    }
  }
  doDestroy() {
    var _a5;
    (_a5 = this._tip) == null ? void 0 : _a5.destroy();
    super.doDestroy();
  }
};
//region Config
__publicField(TimelineSummary, "$name", "TimelineSummary");
TimelineSummary._$name = "TimelineSummary";

// ../Scheduler/lib/Scheduler/feature/Summary.js
var Summary2 = class extends TimelineSummary {
  static get configurable() {
    return {
      /**
       * Array of summary configs which consists of a label and a {@link #config-renderer} function
       *
       * ```javascript
       * new Scheduler({
       *     features : {
       *         summary : {
       *             summaries : [
       *                 {
       *                     label : 'Label',
       *                     renderer : ({ startDate, endDate, eventStore, resourceStore, events, element }) => {
       *                         // return display value
       *                         returns '<div>Renderer output</div>';
       *                     }
       *                 }
       *             ]
       *         }
       *     }
       * });
       * ```
       *
       * @config {SchedulerSummaryOptions[]}
       */
      summaries: null,
      /**
       * Renderer function for a single time axis tick. Should calculate a sum and return HTML as a result.
       *
       * <div class="note">
       * You should never modify any records inside this method.
       * </div>
       *
       * ```javascript
       * new Scheduler({
       *     features : {
       *         summary : {
       *             renderer : ({ startDate, endDate, eventStore, resourceStore, events, element }) => {
       *                 // return display value
       *                 returns '<div>Renderer output</div>';
       *             }
       *         }
       *     }
       * });
       * ```
       *
       * @param {Object} context Rendering context object
       * @param {Date} context.startDate Tick start date
       * @param {Date} context.endDate Tick end date
       * @param {Scheduler.model.EventModel[]} context.events Events which belong to the group
       * @param {Scheduler.data.EventStore} context.eventStore Event store
       * @param {Scheduler.data.ResourceStore} context.resourceStore Resource store
       * @param {HTMLElement} context.element Summary tick container
       * @returns {String} Html content
       * @config {Function}
       */
      renderer: null,
      /**
       * A config object for the {@link Grid.column.Column} used to contain the summary bar.
       * @config {GridColumnConfig}
       */
      verticalSummaryColumnConfig: null
    };
  }
  // Plugin configuration. This plugin chains some of the functions in Grid.
  static get pluginConfig() {
    const config = super.pluginConfig;
    config.chain.push("bindStore", "updateEventStore", "updateResourceStore");
    return config;
  }
  //endregion
  //region Init
  construct(scheduler, config) {
    const me = this;
    me.scheduler = scheduler;
    if (scheduler.isVertical) {
      scheduler.timeAxisSubGrid.resizable = false;
      config.hideFooters = true;
      scheduler.add(scheduler.createSubGrid("right"));
      me.summaryColumn = scheduler.columns.add(ObjectHelper.assign({
        filterable: null,
        region: "right",
        cellCls: "b-grid-footer b-sch-summarybar",
        align: "center",
        sortable: false,
        editor: false,
        groupable: false,
        htmlEncode: false,
        cellMenuItems: false,
        enableHeaderContextMenu: false,
        hidden: me.disabled
      }, me.verticalSummaryColumnConfig))[0];
    }
    super.construct(scheduler, config);
    if (scheduler.isSchedulerBase) {
      me.updateEventStore(scheduler.eventStore);
      me.updateResourceStore(scheduler.resourceStore);
    }
  }
  //endregion
  //region Render
  updateEventStore(eventStore) {
    this.detachListeners("summaryEventStore");
    eventStore.ion({
      name: "summaryEventStore",
      filter: "updateTimelineSummaries",
      thisObj: this
    });
  }
  updateResourceStore(resourceStore) {
    this.detachListeners("summaryResourceStore");
    resourceStore.ion({
      name: "summaryResourceStore",
      filter: "updateTimelineSummaries",
      thisObj: this
    });
  }
  /**
   * Updates summaries.
   * @private
   */
  updateTimelineSummaries() {
    const me = this, { client: timeline } = me, { eventStore, timeAxis, isEngineReady, project } = timeline, summaryContainer = me.summaryBarElement, forResources = me.selectedOnly && timeline.selectedRecords.length ? timeline.selectedRecords : timeline.resourceStore.records;
    if (summaryContainer && isEngineReady && project.isInitialCommitPerformed) {
      const eventsByTick = timeline.getResourcesEventsPerTick(forResources, ({ event }) => {
        return !eventStore.isFiltered || eventStore.records.includes(event);
      });
      Array.from(summaryContainer.children).forEach((element, i) => {
        const tick = timeAxis.getAt(i), events = eventsByTick[i] || [];
        let html = "", tipHtml = `<header>${me.L("L{Summary for}", timeline.getFormattedDate(tick.startDate))}</header>`;
        me.summaries.forEach((config) => {
          const value2 = config.renderer({
            startDate: tick.startDate,
            endDate: tick.endDate,
            resourceStore: timeline.resourceStore,
            eventStore,
            events,
            element
          }), valueHtml = value2 ? `<div class="b-timeaxis-summary-value">${value2}</div>` : "";
          if (me.summaries.length > 1 || value2) {
            html += valueHtml;
          }
          tipHtml += valueHtml ? `<label>${config.label || ""}</label>` + valueHtml : "";
        });
        element.innerHTML = html;
        element._tipHtml = html ? tipHtml : "";
      });
    }
  }
  updateSummaries() {
    if (!this.isConfiguring) {
      this.refreshSummaries();
    }
  }
};
//region Config
__publicField(Summary2, "$name", "Summary");
Summary2._$name = "Summary";
GridFeatureManager.registerFeature(Summary2, false, "Scheduler");

// ../Scheduler/lib/Scheduler/feature/TimeAxisHeaderMenu.js
var setTimeSpanOptions = {
  maintainVisibleStart: true
};
var TimeAxisHeaderMenu = class extends HeaderMenu {
  static get pluginConfig() {
    const config = super.pluginConfig;
    config.chain.push("populateTimeAxisHeaderMenu");
    return config;
  }
  //endregion
  //region Events
  /**
   * This event fires on the owning Scheduler or Gantt widget before the context menu is shown for the time axis header.
   * Allows manipulation of the items to show in the same way as in the {@link #config-processItems}.
   *
   * Returning `false` from a listener prevents the menu from being shown.
   *
   * @event timeAxisHeaderMenuBeforeShow
   * @on-owner
   * @preventable
   * @param {Scheduler.view.Scheduler} source The scheduler
   * @param {Core.widget.Menu} menu The menu
   * @param {Object<String,MenuItemConfig>} items Menu item configs
   * @param {Grid.column.Column} column Time axis column
   */
  /**
   * This event fires on the owning Scheduler or Gantt widget after the context menu is shown for a header
   * @event timeAxisHeaderMenuShow
   * @on-owner
   * @param {Scheduler.view.Scheduler} source The scheduler
   * @param {Core.widget.Menu} menu The menu
   * @param {Object<String,MenuItemConfig>} items Menu item configs
   * @param {Grid.column.Column} column Time axis column
   */
  /**
   * This event fires on the owning Scheduler or Gantt widget when an item is selected in the header context menu.
   * @event timeAxisHeaderMenuItem
   * @on-owner
   * @param {Scheduler.view.Scheduler} source The scheduler
   * @param {Core.widget.Menu} menu The menu
   * @param {Core.widget.MenuItem} item Selected menu item
   * @param {Grid.column.Column} column Time axis column
   */
  //endregion
  construct() {
    super.construct(...arguments);
    if (this.triggerEvent.includes("click") && this.client.zoomOnTimeAxisDoubleClick) {
      this.client.zoomOnTimeAxisDoubleClick = false;
    }
  }
  shouldShowMenu(eventParams) {
    const { column, targetElement } = eventParams, { client } = this;
    if (client.isHorizontal) {
      return (column == null ? void 0 : column.enableHeaderContextMenu) !== false && (column == null ? void 0 : column.isTimeAxisColumn);
    }
    return targetElement.closest(".b-verticaltimeaxis-cell");
  }
  populateTimeAxisHeaderMenu({ items: items2 }) {
    const me = this, { client } = me, dateStep = {
      magnitude: client.timeAxis.shiftIncrement,
      unit: client.timeAxis.shiftUnit
    };
    Object.assign(items2, {
      zoomLevel: {
        text: "L{pickZoomLevel}",
        localeClass: me,
        icon: "b-fw-icon b-icon-search-plus",
        disabled: !client.presets.count || me.disabled,
        weight: 200,
        menu: {
          items: {
            zoomSlider: {
              type: "slider",
              weight: 210,
              minWidth: 130,
              showValue: false,
              // so that we can use the change event which is easier to inject in tests
              triggerChangeOnInput: true
            }
          },
          onBeforeShow({ source: menu }) {
            const [zoom] = menu.items;
            zoom.min = client.minZoomLevel;
            zoom.max = client.maxZoomLevel;
            zoom.value = client.zoomLevel;
            me.zoomDetacher = zoom.ion({ change: "onZoomSliderChange", thisObj: me });
          },
          onHide() {
            var _a5;
            (_a5 = me.zoomDetacher) == null ? void 0 : _a5.call(me);
            me.zoomDetacher = null;
          }
        }
      },
      dateRange: {
        text: "L{activeDateRange}",
        localeClass: me,
        icon: "b-fw-icon b-icon-calendar",
        weight: 300,
        menu: {
          type: "popup",
          cls: "b-sch-timeaxis-menu-daterange-popup",
          defaults: {
            localeClass: me
          },
          items: {
            startDateField: {
              type: "datefield",
              label: "L{startText}",
              weight: 310,
              labelWidth: "6em",
              required: true,
              step: dateStep,
              highlightExternalChange: false,
              picker: {
                internalListeners: {
                  beforeShow: me.updateMinAllowedStartDate,
                  thisObj: me
                }
              },
              internalListeners: {
                change: me.onRangeDateFieldChange,
                thisObj: me
              }
            },
            endDateField: {
              type: "datefield",
              label: "L{endText}",
              weight: 320,
              labelWidth: "6em",
              required: true,
              step: dateStep,
              highlightExternalChange: false,
              picker: {
                internalListeners: {
                  beforeShow: me.updateMaxAllowedEndDate,
                  thisObj: me
                }
              },
              internalListeners: {
                change: me.onRangeDateFieldChange,
                thisObj: me
              }
            },
            leftShiftBtn: {
              type: "button",
              weight: 330,
              cls: "b-left-nav-btn",
              icon: "b-icon b-icon-previous",
              color: "b-blue b-raised",
              flex: 1,
              margin: 0,
              internalListeners: {
                click: me.onLeftShiftBtnClick,
                thisObj: me
              }
            },
            todayBtn: {
              type: "button",
              weight: 340,
              cls: "b-today-nav-btn",
              color: "b-blue b-raised",
              text: "L{todayText}",
              flex: 4,
              internalListeners: {
                click: me.onTodayBtnClick,
                thisObj: me
              }
            },
            rightShiftBtn: {
              type: "button",
              weight: 350,
              cls: "b-right-nav-btn",
              icon: "b-icon b-icon-next",
              color: "b-blue b-raised",
              flex: 1,
              internalListeners: {
                click: me.onRightShiftBtnClick,
                thisObj: me
              }
            }
          },
          internalListeners: {
            paint: me.initDateRangeFields,
            thisObj: me
          }
        }
      }
    });
  }
  onZoomSliderChange({ value: value2 }) {
    this.client.zoomLevel = value2;
  }
  initDateRangeFields({
    source: dateRange,
    firstPaint
  }) {
    if (firstPaint) {
      const { widgetMap } = dateRange;
      this.startDateField = widgetMap.startDateField;
      this.endDateField = widgetMap.endDateField;
    }
    this.initDates();
  }
  initDates() {
    const me = this;
    me.startDateField.suspendEvents();
    me.endDateField.suspendEvents();
    me.startDateField.value = me.startDateFieldInitialValue = me.client.startDate;
    me.endDateField.value = me.endDateFieldInitialValue = me.client.endDate;
    me.startDateField.resumeEvents();
    me.endDateField.resumeEvents();
  }
  updateMaxAllowedEndDate({ source }) {
    source.maxDate = this.client.getAllowedDateRange().endDate;
    return true;
  }
  updateMinAllowedStartDate({ source }) {
    source.minDate = this.client.getAllowedDateRange().startDate;
    return true;
  }
  onRangeDateFieldChange({ source }) {
    const me = this, startDateChanged = source === me.startDateField, { client } = me, { timeAxis } = client, startDate = me.startDateFieldInitialValue && !startDateChanged ? me.startDateFieldInitialValue : me.startDateField.value;
    let endDate = me.endDateFieldInitialValue && startDateChanged ? me.endDateFieldInitialValue : me.endDateField.value;
    if (startDateChanged) {
      me.startDateFieldInitialValue = null;
    } else {
      me.endDateFieldInitialValue = null;
    }
    if (!(endDate - startDate)) {
      endDate = DateHelper.add(endDate, timeAxis.shiftIncrement, timeAxis.shiftUnit);
    } else if (endDate < startDate) {
      endDate = DateHelper.add(startDate, timeAxis.shiftIncrement, timeAxis.shiftUnit);
    }
    client.setTimeSpan(startDate, endDate, setTimeSpanOptions);
    me.initDates();
  }
  onLeftShiftBtnClick() {
    this.client.timeAxis.shiftPrevious();
    this.initDates();
  }
  onTodayBtnClick() {
    const today = DateHelper.clearTime(/* @__PURE__ */ new Date());
    this.client.setTimeSpan(today, DateHelper.add(today, 1, "day"));
    this.initDates();
  }
  onRightShiftBtnClick() {
    this.client.timeAxis.shiftNext();
    this.initDates();
  }
};
//region Config
__publicField(TimeAxisHeaderMenu, "$name", "TimeAxisHeaderMenu");
__publicField(TimeAxisHeaderMenu, "configurable", {
  menu: {
    $config: ["lazy", "nullify"],
    value: {
      type: "menu",
      autoShow: false,
      closeAction: "hide",
      align: {
        monitorResize: false
      },
      scrollAction: null,
      constrainTo: globalThis
    }
  },
  /**
   * A function called before displaying the menu that allows manipulations of its items.
   * Returning `false` from this function prevents the menu being shown.
   *
   * ```javascript
   *   features         : {
   *       timeAxisHeaderMenu : {
   *           processItems({ items }) {
   *               // Add or hide existing items here as needed
   *               items.myAction = {
   *                   text   : 'Cool action',
   *                   icon   : 'b-fa b-fa-fw b-fa-ban',
   *                   onItem : () => console.log('Some coolness'),
   *                   weight : 300 // Move to end
   *               };
   *
   *               // Hide zoom slider
   *               items.zoomLevel.hidden = true;
   *           }
   *       }
   *   },
   * ```
   *
   * @config {Function}
   * @param {Object} context An object with information about the menu being shown.
   * @param {Scheduler.feature.TimeAxisHeaderMenu} context.feature A reference to this feature.
   * @param {Event} context.domEvent The initiating event.
   * @param {Event} context.event DEPRECATED: The initiating event.
   * @param {Number[]} context.point The client `X` and `Y` position of the initiating event.
   * @param {HTMLElement} context.targetElement The target to which the menu is being applied.
   * @param {Object<String,MenuItemConfig|Boolean>} context.items An object containing the
   *   {@link Core.widget.MenuItem menu item} configs keyed by their id.
   * @returns {Boolean|null} Returning `false` from this function prevents the menu being shown.
   * @preventable
   */
  processItems: null,
  /**
   * This is a preconfigured set of items used to create the default context menu.
   *
   * The `items` provided by this feature are listed in the intro section of this class. You can
   * configure existing items by passing a configuration object to the keyed items.
   *
   * To remove existing items, set corresponding keys `null`:
   *
   * ```javascript
   * const scheduler = new Scheduler({
   *     features : {
   *         timeAxisHeaderMenu : {
   *             items : {
   *                 eventsFilter : null
   *             }
   *         }
   *     }
   * });
   * ```
   *
   * See the feature config in the above example for details.
   *
   * @config {Object<String,MenuItemConfig|Boolean|null>} items
   */
  items: null,
  type: "timeAxisHeader"
});
TimeAxisHeaderMenu._$name = "TimeAxisHeaderMenu";
GridFeatureManager.registerFeature(TimeAxisHeaderMenu, true, ["Scheduler", "TimelineHistogram", "Gantt"]);

// ../Scheduler/lib/Scheduler/feature/TimeRanges.js
var TimeRanges = class extends AbstractTimeRanges.mixin(AttachToProjectMixin_default) {
  //endregion
  /**
   * Returns the TimeRanges which occur within the client Scheduler's time axis.
   * @property {Scheduler.model.TimeSpan[]}
   */
  get timeRanges() {
    var _a5;
    const me = this, { store } = me;
    if (!me._timeRanges) {
      let result = [];
      if (store) {
        result = store.records;
        if ((_a5 = store.recurringEvents) == null ? void 0 : _a5.size) {
          const {
            startDate,
            endDate
          } = me.client.timeAxis;
          result = result.flatMap((timeSpan) => {
            if (timeSpan.isRecurring) {
              return timeSpan.getOccurrencesForDateRange(startDate, endDate);
            }
            return timeSpan;
          });
        }
      }
      if (me.showCurrentTimeLine && me.currentTimeLine) {
        result = [me.currentTimeLine, ...result];
      }
      me._timeRanges = result;
    }
    return me._timeRanges;
  }
  //endregion
  //region Current time line
  attachToProject(project) {
    var _a5, _b2;
    const me = this;
    me.attachToTimeRangeStore(project.timeRangeStore);
    super.attachToProject(project);
    (_a5 = me.projectTimeZoneChangeDetacher) == null ? void 0 : _a5.call(me);
    if (me.showCurrentTimeLine) {
      me.projectTimeZoneChangeDetacher = (_b2 = me.client.project) == null ? void 0 : _b2.ion({
        timeZoneChange: "updateCurrentTimeLine",
        thisObj: me
      });
      if (me.currentTimeLine) {
        me.updateCurrentTimeLine();
      }
    }
  }
  initCurrentTimeLine() {
    var _a5, _b2, _c2;
    const me = this;
    if (me.currentTimeLine || !me.showCurrentTimeLine) {
      return;
    }
    const data = typeof me.showCurrentTimeLine === "object" ? me.showCurrentTimeLine : {};
    me.currentTimeLine = (((_a5 = me.store) == null ? void 0 : _a5.modelClass) || TimeSpan).new({
      id: "currentTime",
      cls: "b-sch-current-time"
    }, data);
    me.currentTimeInterval = me.setInterval(() => me.updateCurrentTimeLine(), me.currentTimeLineUpdateInterval);
    me._timeRanges = null;
    me.updateCurrentTimeLine();
    (_c2 = (_b2 = me.client).syncSplits) == null ? void 0 : _c2.call(_b2, (other) => {
      var _a6, _b3, _c3;
      return (_c3 = (_b3 = (_a6 = other.features) == null ? void 0 : _a6.timeRanges) == null ? void 0 : _b3.initCurrentTimeLine) == null ? void 0 : _c3.call(_b3);
    });
  }
  updateCurrentTimeLine() {
    var _a5;
    const me = this, { currentTimeLine, client } = me;
    currentTimeLine.timeZone = (_a5 = me.project) == null ? void 0 : _a5.timeZone;
    currentTimeLine.setLocalDate("startDate", /* @__PURE__ */ new Date());
    currentTimeLine.endDate = currentTimeLine.startDate;
    if (!currentTimeLine.originalData.name) {
      currentTimeLine.name = DateHelper.format(currentTimeLine.startDate, me.currentDateFormat);
    }
    me.renderRanges();
    client.trigger("currentTimelineUpdate", { date: currentTimeLine.startDate });
  }
  hideCurrentTimeLine() {
    var _a5, _b2;
    const me = this;
    if (!me.currentTimeLine) {
      return;
    }
    me.clearInterval(me.currentTimeInterval);
    me.currentTimeLine = null;
    me.refresh();
    (_b2 = (_a5 = me.client).syncSplits) == null ? void 0 : _b2.call(_a5, (other) => {
      var _a6, _b3, _c2;
      return (_c2 = (_b3 = (_a6 = other.features) == null ? void 0 : _a6.timeRanges) == null ? void 0 : _b3.hideCurrentTimeLine) == null ? void 0 : _c2.call(_b3);
    });
  }
  updateShowCurrentTimeLine(show) {
    if (show) {
      this.initCurrentTimeLine();
    } else {
      this.hideCurrentTimeLine();
    }
    this.client.trigger("stateChange");
  }
  //endregion
  //region Menu items
  /**
   * Adds a menu item to show/hide current time line.
   * @param {Object} options Contains menu items and extra data retrieved from the menu target.
   * @param {Grid.column.Column} options.column Column for which the menu will be shown
   * @param {Object<String,MenuItemConfig|Boolean|null>} options.items A named object to describe menu items
   * @internal
   */
  populateTimeAxisHeaderMenu({ items: items2 }) {
    items2.currentTimeLine = {
      weight: 400,
      text: this.L("L{showCurrentTimeLine}"),
      checked: this.currentTimeLine,
      onToggle: ({ checked }) => {
        if (!this.showCurrentTimeLine) {
          this.showCurrentTimeLine = checked;
        } else {
          this.updateShowCurrentTimeLine(checked);
        }
      }
    };
  }
  //endregion
  //region Store
  attachToStore(store) {
    const me = this;
    if (me.storeDetacher) {
      me.storeDetacher();
    }
    me.storeDetacher = store.ion({
      change: "onStoreChange",
      refresh: "onStoreChange",
      thisObj: me
    });
    me._timeRanges = null;
    me.renderRanges();
  }
  /**
   * Returns the {@link Core.data.Store} used by this feature.
   * This is always the owning Scheduler/Gantt's Project TimeRangeStore.
   * @member {Core.data.Store} store
   * @readonly
   * @category Misc
   */
  get store() {
    var _a5;
    return (_a5 = this.client.project) == null ? void 0 : _a5.timeRangeStore;
  }
  // Called by ProjectConsumer after a new store is assigned at runtime
  attachToTimeRangeStore(store) {
    const { client } = this;
    this.attachToStore(store);
    if (client.timeRanges && !client._timeRangesExposed) {
      store.add(client.timeRanges);
      delete client.timeRanges;
    }
  }
  resolveTimeRangeRecord(el) {
    const id2 = el.closest(this.baseSelector).dataset.id;
    if (id2 === "currentTime") {
      return this.currentTimeLine;
    }
    return this.store.getById(id2);
  }
  onStoreChange({ type, action }) {
    const me = this;
    me._timeRanges = null;
    if (me.disabled || !me.client.isVisible || me.isConfiguring || type === "refresh" && action !== "batch") {
      return;
    }
    me.client.runWithTransition(() => me.renderRanges(), !me.client.refreshSuspended);
  }
  //endregion
  //region Drag
  onDragStart(event) {
    const me = this, { context } = event, record = me.resolveTimeRangeRecord(context.element.closest(me.baseSelector)), rangeBodyEl = me.getBodyElementByRecord(record);
    context.relatedElements = [rangeBodyEl];
    Object.assign(context, {
      record,
      rangeBodyEl,
      originRangeX: event.source.getX(rangeBodyEl),
      originRangeY: event.source.getY(rangeBodyEl)
    });
    context.element.retainElement = true;
    rangeBodyEl.retainElement = true;
    super.onDragStart(event);
    me.showTip(context);
  }
  onDrop(event) {
    const { context } = event, { rangeBodyEl, element } = context;
    element.retainElement = void 0;
    rangeBodyEl.retainElement = void 0;
    if (!context.valid) {
      return this.onInvalidDrop({ context });
    }
    const me = this, { client } = me, { record } = context, coord = client.isHorizontal ? context.newX : context.newY, newStart = client.getDateFromVirtualCoord({ coord, roundingMethod: "round" }), wasModified = record.startDate - newStart !== 0;
    if (wasModified) {
      record.setStartDate(newStart);
    } else {
      me.drag.abort();
    }
    me.destroyTip();
    super.onDrop(event);
  }
  //endregion
  //region Resize
  onResizeStart({ context }) {
    const me = this, record = me.resolveTimeRangeRecord(context.element.closest(me.baseSelector)), rangeBodyEl = me.getBodyElementByRecord(record);
    Object.assign(context, {
      record,
      rangeBodyEl
    });
    me.showTip(context);
    super.onResizeStart(...arguments);
  }
  onResizeDrag({ source, context }) {
    const me = this, { rangeBodyEl } = context, { client } = me, { isHorizontal } = client, startPos = isHorizontal ? context.newX : context.newY, endPos = isHorizontal ? context.newX + context.newWidth : context.newY + context.newHeight, startDate = client.getDateFromVirtualCoord({ coord: startPos, roundingMethod: "round" }), endDate = client.getDateFromVirtualCoord({ coord: endPos, roundingMethod: "round" });
    if (!isHorizontal) {
      if (context.edge === "top") {
        source.setY(rangeBodyEl, context.newY);
      }
      rangeBodyEl.style.height = context.newHeight + "px";
    } else {
      if (context.edge === "left") {
        source.setX(rangeBodyEl, context.newX);
      }
      rangeBodyEl.style.width = context.newWidth + "px";
    }
    me.updateDateIndicator({ startDate, endDate });
  }
  onResize({ context }) {
    if (!context.valid) {
      return this.onInvalidDrop({ context });
    }
    const me = this, { client } = me, { record } = context, startPos = client.isHorizontal ? context.newX : context.newY, endPos = client.isHorizontal ? context.newX + context.newWidth : context.newY + context.newHeight, newStart = client.getDateFromVirtualCoord({ coord: startPos, roundingMethod: "round" }), newEnd = client.getDateFromVirtualCoord({ coord: endPos, roundingMethod: "round" }), isStart = context.edge === "left" || context.edge === "top", wasModified = isStart && record.startDate - newStart !== 0 || newEnd && record.endDate - newEnd !== 0;
    if (wasModified && newEnd > newStart) {
      if (isStart) {
        record.setStartDate(newStart, false);
      } else {
        record.setEndDate(newEnd, false);
      }
    } else {
      me.onInvalidResize({ context });
    }
    me.destroyTip();
  }
  onInvalidResize({ context }) {
    const me = this;
    me.resize.reset();
    context.rangeBodyEl.parentElement.lastDomConfig = context.rangeBodyEl.lastDomConfig = context.element.lastDomConfig = null;
    me.renderRanges();
    me.destroyTip();
  }
  //endregion
  //region state
  get state() {
    return { showCurrentTimeLine: this.showCurrentTimeLine };
  }
  //endregion
};
//region Config
/**
 * Fires on the owning Scheduler/Gantt when the line indicating the current time is updated (see {@link #config-currentTimeLineUpdateInterval}).
 * @event currentTimelineUpdate
 * @on-owner
 * @param {Scheduler.view.Scheduler} source The scheduler
 * @param {Date} date The current date
 */
__publicField(TimeRanges, "stateProperties", ["showCurrentTimeLine"]);
__publicField(TimeRanges, "$name", "TimeRanges");
__publicField(TimeRanges, "configurable", {
  /**
   * The interval (as amount of ms) defining how frequently the current timeline will be updated
   * @config {Number}
   * @default
   * @category Misc
   */
  currentTimeLineUpdateInterval: 1e4,
  /**
   * The date format to show in the header for the current time line (when {@link #config-showCurrentTimeLine} is configured).
   * See {@link Core.helper.DateHelper} for the possible formats to use.
   * @config {String}
   * @default
   * @category Common
   */
  currentDateFormat: "HH:mm",
  /**
   * Show a line indicating current time. Either `true` or `false` or a {@link Scheduler.model.TimeSpan}
   * configuration object to apply to this special time range (allowing you to provide a custom text):
   *
   * ```javascript
   * showCurrentTimeLine : {
   *     name : 'Now'
   * }
   * ```
   *
   * The line carries the CSS class name `b-sch-current-time`, and this may be used to add custom styling to it.
   *
   * @prp {Boolean|TimeSpanConfig}
   * @default false
   * @category Common
   */
  showCurrentTimeLine: {
    $config: "lazy",
    value: null
  }
});
TimeRanges._$name = "TimeRanges";
GridFeatureManager.registerFeature(TimeRanges, false, ["TimelineBase"]);

// ../Scheduler/lib/Scheduler/feature/TimeSelection.js
var TimeSelection = class extends AbstractTimeRanges.mixin(HeaderDragSelect_default) {
  /**
   * @hideconfigs enableResizing, showTooltip, dragTipTemplate, cls, showHeaderElements
   */
  /**
   * @hideproperties showHeaderElements, timeRanges
   */
  /**
   * @hidefunctions getTipHtml, shouldRenderRange
   */
  static get configurable() {
    return {
      enableResizing: true,
      showTooltip: false,
      dragTipTemplate: null,
      cls: "b-selected-time-span",
      dragHelperConfig: {
        // Data will be updated on drag, no need for DragHelper to touch the element
        skipUpdatingElement: true
      },
      resizeHelperConfig: {
        // Data will be updated on resize, no need for ResizeHelper to touch the element
        skipUpdatingElement: true
      },
      /**
       * Template used to generate the tooltip contents when hovering the time selection header element.
       *
       * ```javascript
       * const scheduler = new Scheduler({
       *   features : {
       *     timeSelection : {
       *       tooltipTemplate({ startDate, endDate }) {
       *           const count = this.client.getAvailableResources(startDate, endDate).length;
       *           return `${count || 'No'} available resource(s)`;
       *       }
       *     }
       *   }
       * });
       * ```
       *
       * @config {Function} tooltipTemplate
       * @param {Object} data Tooltip data
       * @param {Date} data.startDate
       * @param {Date} data.endDate
       * @returns {String} String representing the HTML markup
       * @category Common
       */
      /**
       * Specify `true` to enable a drag-drop gesture to select the time span.
       * @prp {Boolean}
       * @default false
       */
      enableDragSelect: false,
      /**
       * Function used to generate the HTML content for the selected time span's header element.
       *
       * If you want to include an icon or similar to clear the selection on click, make sure to set
       * `date-ref="closeButton"` on it.
       *
       * ```javascript
       * const scheduler = new Scheduler({
       *     features : {
       *         timeSelection : {
       *             headerRenderer({ timeRange }) {
       *                 return `
       *                     ${DateHelper.format(timeRange.startDate, 'LL')}
       *                     <div class="close" data-ref="closeButton></div>
       *                 `;
       *             }
       *         }
       *     }
       * });
       * ```
       * @config {Function} headerRenderer
       * @param {Object} data Render data
       * @param {Object} data.timeRange
       * @param {Date} data.timeRange.startDate
       * @param {Date} data.timeRange.endDate
       * @returns {String}
       * @category Common
       */
      headerRenderer({ timeRange }) {
        const { dateFormat } = this.client.timeAxisViewModel.bottomHeader;
        return `<span class="b-selection-start">${DateHelper.format(timeRange.startDate, dateFormat)}</span>
                        <span class="b-selection-end">${DateHelper.format(timeRange.endDate, dateFormat)}</span>
                        <i class='b-icon b-icon-close' data-ref="closeButton" data-btip="L{Popup.close}"></i>`;
      },
      /**
       * The selected time span, which can be set using simple `startDate` and `endDate` properties
       * @prp {Object} selectedTimeSpan
       * @property {Date} selectedTimeSpan.startDate The start date of the selected time span
       * @property {Date} selectedTimeSpan.endDate The end date of the selected time span
       */
      selectedTimeSpan: {
        $config: null,
        value: null,
        default: null
      }
    };
  }
  //endregion
  //region Init & destroy
  onUIReady() {
    const me = this;
    super.onUIReady();
    if (me.hoverTooltip) {
      me.hoverTooltip.getHtml = () => me.tooltipTemplate({ ...me.selectedTimeSpan });
    }
    me.client.ion({
      timeAxisHeaderMouseDown: "onTimeAxisHeaderMouseDown",
      timeAxisHeaderClick: "onTimeAxisHeaderClick",
      thisObj: me
    });
  }
  onTimeAxisHeaderMouseDown({ startDate, endDate }) {
    if (!this.disabled && this.enableDragSelect) {
      this.selectedTimeSpan = null;
    }
  }
  onTimeAxisHeaderClick({ startDate, endDate }) {
    if (!this.disabled) {
      this.selectedTimeSpan = {
        startDate,
        endDate
      };
    }
  }
  onTimeRangeClick(event) {
    super.onTimeRangeClick(event);
    const me = this;
    if (!me.disabled && event.target.closest(".b-selected-time-span")) {
      if (event.target.matches('[data-ref="closeButton"]')) {
        me.selectedTimeSpan = null;
      } else {
        me.client.trigger("timeSelectionElementClick", {
          ...me.selectedTimeSpan,
          domEvent: event
        });
      }
    }
  }
  updateFromCoords(x, y, width, height) {
    const { client } = this, size = client.isHorizontal ? width : height, start = client.isHorizontal ? x : y, startDate = client.getDateFromVirtualCoord({ coord: start, roundingMethod: "round" }) || client.startDate, endDate = client.getDateFromVirtualCoord({ coord: start + size, roundingMethod: "round" }) || client.endDate;
    this.selectedTimeSpan = {
      startDate,
      endDate
    };
  }
  onResizeStart({ source }) {
    const {
      timeAxisViewModel,
      timeResolution
    } = this.client, resolutionDurationMS = DateHelper.asMilliseconds(timeResolution.increment, timeResolution.unit);
    this.resize.minWidth = timeAxisViewModel.getDistanceForDuration(resolutionDurationMS);
    super.onResizeStart(...arguments);
  }
  onResizeDrag({ context }) {
    var _a5, _b2;
    this.updateFromCoords(
      context.newX,
      context.newY,
      (_a5 = context.newWidth) != null ? _a5 : context.elementWidth,
      (_b2 = context.newHeight) != null ? _b2 : context.elementHeight
    );
  }
  onDragStart(event) {
    super.onDragStart(event);
    const { context } = event;
    context.elementWidth = context.element.offsetWidth;
    context.elementHeight = context.element.offsetHeight;
  }
  onDrag({ context }) {
    this.updateFromCoords(
      context.newX,
      context.newY,
      context.elementWidth,
      context.elementHeight
    );
  }
  onDrop() {
    this.client.element.classList.remove("b-dragging-timerange");
  }
  onDragReset() {
    this.refresh();
  }
  get timeRanges() {
    return this.selectedTimeSpan ? [this.selectedTimeSpan] : [];
  }
  // Always render the selection
  shouldRenderRange() {
    return true;
  }
  changeSelectedTimeSpan(timeSpan) {
    if (timeSpan) {
      timeSpan.id = "time-selection";
    }
    return timeSpan;
  }
  updateSelectedTimeSpan(timeSpan) {
    if (!this.isConfiguring) {
      this.refresh();
    }
    this.client.trigger("timeSelectionChange", {
      ...timeSpan != null ? timeSpan : {}
    });
  }
  onTimeSpanDragSelected({ startDate, endDate }) {
    this.selectedTimeSpan = {
      startDate,
      endDate
    };
  }
};
//region Default config
__publicField(TimeSelection, "$name", "TimeSelection");
TimeSelection._$name = "TimeSelection";
GridFeatureManager.registerFeature(TimeSelection, false, ["TimelineBase"]);

// ../Scheduler/lib/Scheduler/widget/TickEditor.js
var TickEditor = class extends Editor {
  async onEditorKeyDown(event) {
    if (event.key === "Tab" || event.key === "Enter") {
      const me = this, { record, owner } = me;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (event.key === "Tab") {
        const adjacentDate = owner.getAdjacentDate(record.startDate, !event.shiftKey);
        if (adjacentDate) {
          await me.completeEdit();
          owner.editTickCell({ id: record.resourceId }, adjacentDate);
        }
      } else if (event.key === "Enter") {
        const adjacentResource = owner.getAdjacentResource(record.resourceId, !event.shiftKey);
        if (adjacentResource) {
          await me.completeEdit();
          owner.editTickCell(adjacentResource, record.startDate);
        }
      }
    } else {
      super.onEditorKeyDown(event);
    }
  }
};
__publicField(TickEditor, "$name", "TickEditor");
// Factoryable type name
__publicField(TickEditor, "type", "tickeditor");
__publicField(TickEditor, "configurable", {
  appendToTargetParent: true,
  cls: "b-tick-editor",
  inputField: {
    type: "numberfield",
    autoSelect: true
  }
});
TickEditor.initClass();
TickEditor._$name = "TickEditor";

// ../Scheduler/lib/Scheduler/feature/TickCells.js
var TickCells = class extends ResourceTimeRangesBase {
  changeResourceTickStore(store) {
    return Store.getStore(store, ResourceTickStore);
  }
  updateResourceTickStore(store, oldStore) {
    this.detachStore(oldStore);
    store = Store.getStore(store, ResourceTickStore);
    if (!store.id) {
      store.id = "resourceTicks";
    }
    store.storage.addIndex({
      property: "indexKey"
    });
    this.attachStore(store);
  }
  // Detach a store, stop relaying its change events
  detachStore(store) {
    store && this.detachListeners(store.$$name);
  }
  // Attach to a store, relaying its change events
  attachStore(store) {
    if (this.syncDataOnLoad) {
      store.syncDataOnLoad = this.syncDataOnLoad;
    }
    store.ion({
      name: store.$$name,
      change: "internalOnStoreChange",
      thisObj: this
    });
  }
  updateResourceTicksData(ticks) {
    this.resourceTickStore.data = ticks;
  }
  updateClient(client) {
    client.ion({
      scheduleDblClick: "onTimeAxisCellDblClick",
      thisObj: this
    });
  }
  //region Rendering
  getEventsToRender(resource, events) {
    const { client } = this, { timeAxis } = client;
    if (!this.disabled) {
      const firstTick = Math.floor(timeAxis.getTickFromDate(timeAxis.startDate)), lastTick = Math.ceil(timeAxis.getTickFromDate(timeAxis.endDate));
      for (let tick = firstTick; tick <= lastTick; tick++) {
        const startDate = timeAxis.getDateFromTick(tick), tickRecord = this.getResourceTickRecord(resource, startDate);
        tickRecord && events.push(tickRecord);
      }
    }
    return events;
  }
  shouldInclude({ isTickCell }) {
    return isTickCell;
  }
  generateElementId(record) {
    return `tick-cell-${record.id}`;
  }
  getElementFromResourceTimeRangeRecord(record) {
    return this.canvas.syncIdMap[`tick-cell-${record.domId}`];
  }
  //endregion
  //region Data
  tickRenderer(resourceTick) {
    return resourceTick.value;
  }
  onEventDataGenerated(renderData) {
    const me = this, { client } = me, { eventRecord: resourceTick } = renderData;
    if (me.shouldInclude(resourceTick)) {
      if (client.isVertical) {
        renderData.width = client.getResourceWidth(renderData.resourceRecord);
      } else {
        renderData.top = 0;
      }
      renderData.fillSize = true;
      renderData.wrapperCls[me.rangeCls] = 1;
      renderData.wrapperCls["b-sch-style-none"] = 0;
      renderData.wrapperCls["b-sch-color-none"] = 0;
      renderData.cls[me.cls] = 1;
      renderData.entityName = me.entityName;
      renderData.eventId = me.generateElementId(resourceTick);
      const rendererResult = me.tickRenderer(resourceTick);
      if (client.isVertical && typeof rendererResult !== "object") {
        renderData.eventContent = {
          text: rendererResult
        };
      }
      renderData.children = ArrayHelper.asArray(rendererResult);
    }
  }
  internalOnStoreChange(event) {
    this.client.refresh();
    return this.trigger("change", { store: event.source, ...event, source: this });
  }
  getResourceTickRecord(resource, startDate) {
    return this.resourceTickStore.storage.getBy("indexKey", `${startDate.getTime()}-${resource.id}`);
  }
  //endregion
  //region Editing
  editTickCell(resourceRecord, tickStartDate) {
    const me = this, { client } = me;
    let tickRecord = me.getResourceTickRecord(resourceRecord, tickStartDate);
    if (!tickRecord) {
      [tickRecord] = me.resourceTickStore.add({
        resourceId: resourceRecord.id,
        startDate: tickStartDate,
        duration: client.timeAxis.increment,
        durationUnit: client.timeAxis.unit
      });
    }
    const target = me.getElementFromResourceTimeRangeRecord(tickRecord);
    me.editor = new TickEditor({
      appendTo: me.canvas,
      owner: me
    });
    me.editor.startEdit({
      target,
      record: tickRecord,
      field: "value"
    });
  }
  getNextDate(date2) {
    return this.getAdjacentDate(date2);
  }
  getPrevDate(date2) {
    return this.getAdjacentDate(date2, false);
  }
  getAdjacentDate(date2, next = true) {
    const { timeAxis } = this.client, adjacentDate = DateHelper.add(date2, timeAxis.increment * (next ? 1 : -1), timeAxis.unit);
    if (adjacentDate >= timeAxis.startDate && adjacentDate < timeAxis.endDate) {
      return adjacentDate;
    }
    return null;
  }
  getNextResource(resource) {
    return this.getAdjacentResource(resource);
  }
  getPrevResource(resource) {
    return this.getAdjacentResource(resource, false);
  }
  getAdjacentResource(resource, next = true) {
    resource = this.client.store.getById(resource);
    return this.client.store[next ? "getNext" : "getPrev"](resource);
  }
  onTimeAxisCellDblClick({ resourceRecord, tickStartDate }) {
    if (this.showEditor) {
      this.editTickCell(resourceRecord, tickStartDate);
    }
  }
  //endregion
};
__publicField(TickCells, "$name", "TickCells");
__publicField(TickCells, "configurable", {
  /**
   * Store that holds resource ticks - instances of {@link Scheduler.model.ResourceTickModel}.
   * A store will be automatically created if none is specified.
   * @config {ResourceTickStoreConfig|Scheduler.data.ResourceTickStore}
   * @category Models & Stores
   */
  resourceTickStore: {},
  /**
   * Used to configure tick entity name
   *
   * @config {String}
   * @default
   */
  entityName: "tickCell",
  /**
   * Used to configure tick cell wrapper class
   *
   * @config {String}
   * @default
   */
  rangeCls: "b-sch-tick-cell",
  /**
   * Used to configure tick cell class
   *
   * @config {String}
   * @default
   */
  cls: "",
  /**
   * The initial data, to fill the {@link #config-resourceTickStore} with.
   * Should be an array of {@link Scheduler.model.ResourceTickModel} or configuration objects.
   *
   * @config {ResourceTickModelConfig[]|Scheduler.model.ResourceTickModel[]}
   * @category Legacy inline data
   */
  resourceTicksData: null,
  /**
   * Renderer function. Should return textual content or a {@link Core.helper.DomHelper#typedef-DomConfig} object.
   *
   * ```javascript
   * new Scheduler({
   *     features : {
   *         tickCells : {
   *             tickRenderer : resourceTick => {
   *                  const
   *                      hours = Math.floor(resourceTick.value),
   *                      hourFraction = 60 * (value - hours);
   *                  return value ? `${hours}:${String(hourFraction).padStart(2, '0')}` : '';
   *             }
   *         }
   *     }
   * });
   * ```
   *
   * @config {Function} tickRenderer
   * @param {Scheduler.model.ResourceTickModel} resourceTick Tick value to render
   * @returns {String|DomConfig} Either textual content or a DomConfig object
   */
  /**
   * Config used to show or not the tick editor on cell double click.
   *
   * @config {Boolean}
   * @default true
   */
  showEditor: true
});
TickCells._$name = "TickCells";
GridFeatureManager.registerFeature(TickCells, false, ["Scheduler", "SchedulerPro", "Gantt"]);

// ../Scheduler/lib/Scheduler/feature/TreeSummary.js
var TreeSummary = class extends TickCells {
  // Plugin configuration.
  static get pluginConfig() {
    return {
      before: ["onInternalPaint", "internalOnVisibleDateRangeChange"],
      chain: ["getEventsToRender", "onEventDataGenerated", "noFeatureElementsInAxis"],
      override: ["matchScheduleCell", "resolveResourceRecord"]
    };
  }
  changeRenderer(renderer) {
    if (!renderer) {
      throw new Error("The TreeSummary feature requires defining a `renderer` function");
    }
    return renderer;
  }
  onInternalPaint({ firstPaint }) {
    super.onInternalPaint(...arguments);
    if (firstPaint) {
      this.client.ion({
        treeGroupChange: () => this.updateResourceTickCell()
      });
    }
  }
  attachToEventStore(store) {
    store.ion({
      name: "eventStore",
      change: "onEventStoreChange",
      buffer: 1,
      thisObj: this
    });
  }
  attachToResourceStore(store) {
    store.ion({
      name: "resourceStore",
      change: "onResourceStoreChange",
      buffer: 1,
      thisObj: this
    });
  }
  doDisable(disable) {
    super.doDisable(disable);
    if (!this.isConfiguring) {
      this.client.renderRows();
    }
  }
  //endregion
  //region Events
  /**
   * Triggered for mouse down on a tick cell. Only triggered if the TreeSummary feature is configured
   * with `{@link #config-enableMouseEvents}: true`.
   * @event tickCellMouseDown
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.feature.TreeSummary} feature The TreeSummary feature
   * @param {Date} startDate Tick start date
   * @param {Date} endDate Tick end date
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {MouseEvent} domEvent Browser event
   * @on-owner
   */
  /**
   * Triggered for mouse up on a tick cell. Only triggered if the TreeSummary feature is configured
   * with `{@link #config-enableMouseEvents}: true`.
   * @event tickCellMouseUp
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.feature.TreeSummary} feature The TreeSummary feature
   * @param {Date} startDate Tick start date
   * @param {Date} endDate Tick end date
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {MouseEvent} domEvent Browser event
   * @on-owner
   */
  /**
   * Triggered for click on a tick cell. Only triggered if the TreeSummary feature is configured with
   * `{@link #config-enableMouseEvents}: true`.
   * @event tickCellClick
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.feature.TreeSummary} feature The TreeSummary feature
   * @param {Date} startDate Tick start date
   * @param {Date} endDate Tick end date
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {MouseEvent} domEvent Browser event
   * @on-owner
   */
  /**
   * Triggered for double-click on a tick cell. Only triggered if the TreeSummary feature is configured
   * with `{@link #config-enableMouseEvents}: true`.
   * @event tickCellDblClick
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.feature.TreeSummary} feature The TreeSummary feature
   * @param {Date} startDate Tick start date
   * @param {Date} endDate Tick end date
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {MouseEvent} domEvent Browser event
   * @on-owner
   */
  /**
   * Triggered for right-click on a tick cell. Only triggered if the TreeSummary feature is configured
   * with `{@link #config-enableMouseEvents}: true`.
   * @event tickCellContextMenu
   * @param {Scheduler.view.Scheduler} source This Scheduler
   * @param {Scheduler.feature.TreeSummary} feature The TreeSummary feature
   * @param {Date} startDate Tick start date
   * @param {Date} endDate Tick end date
   * @param {Scheduler.model.ResourceModel} resourceRecord Resource record
   * @param {MouseEvent} domEvent Browser event
   * @on-owner
   */
  internalOnVisibleDateRangeChange() {
    if (!this.disabled) {
      this.updateResourceTickCell();
    }
  }
  async onEventStoreChange() {
    await this.client.project.commitAsync();
    if (!this.disabled) {
      this.updateResourceTickCell();
    }
  }
  async onResourceStoreChange({ action, records }) {
    await this.client.project.commitAsync();
    if (!this.disabled) {
      if (action === "remove" || action === void 0) {
        this.updateResourceTickCell();
      } else {
        const resources = /* @__PURE__ */ new Set();
        records.forEach((record) => {
          record.bubble((parent) => {
            resources.add(parent);
            return !record.isParent;
          });
        });
        this.updateResourceTickCell(resources, false);
      }
    }
  }
  updateDisabled(disabled) {
    if (!this.isConfiguring) {
      if (disabled) {
        this.resourceTickStore.removeAll();
      } else {
        this.updateResourceTickCell();
      }
    }
  }
  //endregion
  updateResourceTickCell(resourceRecords = null, reset = true) {
    const me = this, { client, resourceTickStore } = me, { timeAxis, visibleDateRange } = client;
    if (!me.disabled) {
      const firstTick = Math.floor(timeAxis.getTickFromDate(visibleDateRange.startDate)), lastTick = Math.ceil(timeAxis.getTickFromDate(visibleDateRange.endDate)), ticks = [];
      (resourceRecords && resourceRecords.size !== 0 ? resourceRecords : client.store.storage.allValues).forEach((record) => {
        if (!(record == null ? void 0 : record.isParent)) {
          return;
        }
        for (let tick = firstTick; tick < lastTick; tick++) {
          const startDate = timeAxis.getDateFromTick(tick), endDate = timeAxis.getDateFromTick(tick + 1), summary = me.renderer({
            startDate,
            endDate,
            resourceRecord: record,
            eventStore: client.eventStore,
            resourceStore: client.resourceStore,
            timeline: client
          });
          if (summary || summary === 0) {
            ticks.push({
              id: `${tick}-${record.id}`,
              startDate,
              endDate,
              value: summary,
              resourceId: record.id
            });
          }
        }
      });
      if (reset) {
        me.resourceTicksData = ticks;
      } else {
        const { allValues } = resourceTickStore.storage, ticksToRemove = [];
        for (const res of resourceRecords) {
          for (const record of allValues) {
            if (record.resourceId === res.id) {
              ticksToRemove.push(record);
            }
          }
        }
        client.suspendRefresh();
        resourceTickStore.remove(ticksToRemove);
        resourceTickStore.add(ticks);
        client.resumeRefresh(false);
        client.refresh();
      }
    }
  }
};
__publicField(TreeSummary, "$name", "TreeSummary");
//region Init
__publicField(TreeSummary, "configurable", {
  // region Hidden configs
  /**
   * @hideconfigs resourceTickStore, resourceTicksData, tickRenderer, showEditor
   */
  // endRegion
  rangeCls: "b-sch-tick-summary-cell",
  cls: "b-tree-summary-cell",
  showEditor: false,
  /**
   * Required, renderer function for parent resource summary cells. Used to both calculate and format the summary.
   * Should return textual content or a {@link Core.helper.DomHelper#typedef-DomConfig} object.
   *
   * ```javascript
   * new Scheduler({
   *     features : {
   *         treeSummary : {
   *             renderer({ startDate, endDate, resourceRecord, timeline }) {
   *                  let totalDemandedCapacity = 0;
   *
   *                  resourceRecord.traverse(node => {
   *                     node.events.forEach(task => {
   *                          if (DateHelper.intersectSpans(task.startDate, task.endDate, startDate, endDate)) {
   *                              totalDemandedCapacity += task.demandedCapacity || 0;
   *                          }
   *                      });
   *                  }, true);
   *
   *                  if (timeline.project.effectiveCalendar.isWorkingTime(startDate, endDate)) {
   *                      return 1 - totalDemandedCapacity;
   *                  }
   *
   *                  return 0;
   *             }
   *         }
   *     }
   * });
   * ```
   *
   * @config {Function}
   * @param {Object} context Summary context object
   * @param {Date} context.startDate Tick start date
   * @param {Date} context.endDate Tick end date
   * @param {Scheduler.model.ResourceModel} context.resourceRecord Resource record representing the current row
   * @param {Scheduler.data.EventStore} context.eventStore Event store
   * @param {Scheduler.data.ResourceStore} context.resourceStore Resource store
   * @param {Scheduler.view.TimelineBase} context.timeline The timeline/scheduler instance
   * @returns {String|DomConfig} Either textual content or a DomConfig object
   */
  renderer: false,
  /**
   * Set to `false` to disallow mouse interactions with the rendered range elements. By default, the range elements
   * are reachable with the mouse.
   * @prp {Boolean}
   * @default true
   */
  enableMouseEvents: true
});
TreeSummary._$name = "TreeSummary";
GridFeatureManager.registerFeature(TreeSummary, false, ["Scheduler", "SchedulerPro", "Gantt"]);

// ../Scheduler/lib/Scheduler/feature/export/Utils.js
var ScheduleRange = {
  completeview: "completeview",
  // completedata : 'completedata',
  currentview: "currentview",
  daterange: "daterange"
};

// ../Scheduler/lib/Scheduler/feature/export/exporter/SchedulerExporterMixin.js
var SchedulerExporterMixin_default = (base) => class SchedulerExporterMixin extends base {
  cloneElement(element, target, clear) {
    super.cloneElement(element, target, clear);
    const clonedEl = this.element.querySelector(".b-schedulerbase");
    clonedEl == null ? void 0 : clonedEl.classList.remove(...["fade-in", "slide-from-left", "slide-from-top", "zoom-in"].map((name) => `b-initial-${name}`));
  }
  async prepareComponent(config) {
    const me = this, { client } = config, { isVertical } = client, includeTimeline = client.timeAxisSubGrid.width > 0;
    switch (config.scheduleRange) {
      case ScheduleRange.completeview:
        config.rangeStart = client.startDate;
        config.rangeEnd = client.endDate;
        break;
      case ScheduleRange.currentview: {
        const { startDate, endDate } = client.visibleDateRange;
        config.rangeStart = startDate;
        config.rangeEnd = endDate;
        break;
      }
    }
    await client.waitForAnimations();
    config.infiniteScroll = client.infiniteScroll;
    client.infiniteScroll = false;
    if (includeTimeline) {
      if (config.scheduleRange !== ScheduleRange.currentview) {
        client.setTimeSpan(config.rangeStart, config.rangeEnd);
      }
      if (config.scheduleRange === ScheduleRange.daterange) {
        config.rangeStart = client.startDate;
        config.rangeEnd = client.endDate;
      }
      client.svgCanvas;
    }
    me._oldEnableEventAnimations = client.enableEventAnimations;
    client.enableEventAnimations = false;
    await super.prepareComponent(config);
    const { exportMeta, element } = me, fgCanvasEl = element.querySelector(".b-sch-foreground-canvas"), rtrCanvasEl = element.querySelector(".b-resource-time-range-canvas"), timeAxisEl = element.querySelector(`.b-${isVertical ? "vertical" : "horizontal"}timeaxis`), normalSubGrid = exportMeta.subGrids.normal;
    DomHelper.forEachSelector(element, ".b-sch-canvas", (element2) => {
      let selector = "";
      for (const cls of element2.classList.values()) {
        selector += `.${cls}`;
      }
      const originalElement = client.element.querySelector(selector);
      if (originalElement && originalElement.clientWidth) {
        element2.style.width = `${originalElement.clientWidth}px`;
      }
    });
    exportMeta.includeTimeline = includeTimeline;
    exportMeta.timeAxisHeaders = [];
    exportMeta.timeAxisPlaceholders = [];
    exportMeta.headersCollected = false;
    exportMeta.eventsBoxes = /* @__PURE__ */ new Map();
    exportMeta.resourceTimeRangesMap = /* @__PURE__ */ new Map();
    exportMeta.eventsMap = /* @__PURE__ */ new Map();
    exportMeta.verticalTimeAxisMargin = 0;
    exportMeta.isHorizontal = !isVertical;
    exportMeta.client = client;
    if (includeTimeline) {
      if (config.scheduleRange !== ScheduleRange.completeview) {
        if (isVertical) {
          exportMeta.totalHeight = client.headerHeight + client.getTimeSpanDistance(config.rangeStart, config.rangeEnd) + client.footerHeight;
          exportMeta.verticalTimeAxisMargin = -1 * client.getCoordinateFromDate(config.rangeStart);
        } else {
          exportMeta.totalWidth -= normalSubGrid.width;
          exportMeta.totalWidth += normalSubGrid.width = client.timeAxisViewModel.getDistanceBetweenDates(config.rangeStart, config.rangeEnd);
          if (me.isMultiPageExporter) {
            const horizontalPages = Math.ceil(exportMeta.totalWidth / exportMeta.pageWidth), totalPages = horizontalPages * exportMeta.verticalPages;
            exportMeta.horizontalPages = horizontalPages;
            exportMeta.totalPages = totalPages;
          }
          normalSubGrid.scrollLeft = client.getCoordinateFromDate(config.rangeStart);
        }
      } else if (isVertical) {
        exportMeta.totalHeight = client.headerHeight + client.timeAxisViewModel.totalSize + client.footerHeight;
      }
    }
    if (!includeTimeline) {
      return;
    }
    DomHelper.forEachSelector(timeAxisEl, ".b-sch-header-row", (headerRow) => {
      exportMeta.timeAxisPlaceholders.push(me.createPlaceholder(headerRow));
      exportMeta.timeAxisHeaders.push(/* @__PURE__ */ new Map());
    });
    exportMeta.eventsPlaceholder = me.createPlaceholder(fgCanvasEl, false);
    DomHelper.removeEachSelector(fgCanvasEl, ".b-sch-event-wrap");
    DomHelper.removeEachSelector(me.element, ".b-released");
    if (rtrCanvasEl) {
      exportMeta.resourceTimeRangesPlaceholder = me.createPlaceholder(rtrCanvasEl, false);
      DomHelper.removeEachSelector(rtrCanvasEl, ".b-sch-resourcetimerange");
    }
    const columnLinesCanvas = element.querySelector(".b-column-lines-canvas"), timeRangesHeaderCanvas = element.querySelector(".b-timeranges-header-canvas"), timeRangesBodyCanvas = element.querySelector(".b-timeranges-body-canvas");
    if (client.hasActiveFeature("columnLines") && columnLinesCanvas) {
      exportMeta.columnLinesPlaceholder = me.createPlaceholder(columnLinesCanvas);
      exportMeta.columnLines = "";
    }
    if (timeRangesBodyCanvas && Object.values(client.features).some((f) => f instanceof AbstractTimeRanges)) {
      exportMeta.timeRanges = {};
      if (timeRangesHeaderCanvas) {
        exportMeta.timeRanges.header = "";
        exportMeta.timeRangesHeaderPlaceholder = me.createPlaceholder(timeRangesHeaderCanvas);
      }
      exportMeta.timeRanges.body = "";
      exportMeta.timeRangesBodyPlaceholder = me.createPlaceholder(timeRangesBodyCanvas);
    }
    if (client.hasActiveFeature("dependencies")) {
      client.features.dependencies.fillDrawingCache();
      const svgCanvasEl = element.querySelector(`[id="${client.svgCanvas.getAttribute("id")}"]`);
      if (svgCanvasEl) {
        exportMeta.dependenciesPlaceholder = me.createPlaceholder(svgCanvasEl, false, {
          ns: "http://www.w3.org/2000/svg",
          tag: "path"
        });
        DomHelper.removeEachSelector(svgCanvasEl, ".b-sch-dependency");
      }
    }
  }
  async restoreComponent(config) {
    const { client } = config;
    client.infiniteScroll = config.infiniteScroll;
    client.enableEventAnimations = this._oldEnableEventAnimations;
    await super.restoreComponent(config);
  }
  async onRowsCollected(rows, config) {
    const me = this;
    await super.onRowsCollected(rows, config);
    if (me.exportMeta.includeTimeline) {
      const { pageRangeStart, pageRangeEnd } = me.getCurrentPageDateRange(config);
      if (pageRangeStart && pageRangeEnd) {
        me.renderTimeAxisHeaders(config, pageRangeStart, pageRangeEnd);
        me.renderResourceHeaders(config);
        me.renderLines(config, pageRangeStart, pageRangeEnd);
        me.renderRanges(config, pageRangeStart, pageRangeEnd);
        me.renderEvents(config, rows, pageRangeStart, pageRangeEnd);
      }
    }
  }
  async stateNextPage(config) {
    var _a5, _b2;
    await ((_a5 = super.stateNextPage) == null ? void 0 : _a5.call(this, config));
    const me = this;
    me.exportMeta.eventsBoxes.clear();
    me.exportMeta.eventsMap.clear();
    (_b2 = me.exportMeta.rowsMap) == null ? void 0 : _b2.clear();
    me.exportMeta.resourceTimeRangesMap.clear();
    me.exportMeta.timeAxisHeaders.forEach((headerMap) => headerMap.clear());
    me.exportMeta.columnLines = "";
    me.exportMeta.timeRanges = {};
  }
  getCurrentPageDateRange({ rangeStart, rangeEnd, client }) {
    const me = this, { exportMeta } = me, { horizontalPages, horizontalPosition, pageWidth, subGrids } = exportMeta;
    let pageRangeStart, pageRangeEnd;
    if (horizontalPages > 1) {
      const pageStartX = horizontalPosition * pageWidth, pageEndX = (horizontalPosition + 1) * pageWidth, normalGridX = subGrids.locked.width + subGrids.locked.splitterWidth;
      if (pageEndX <= normalGridX) {
        pageRangeEnd = pageRangeStart = null;
      } else {
        const { scrollLeft = 0 } = subGrids.normal;
        pageRangeStart = client.getDateFromCoordinate(Math.max(pageStartX - normalGridX + scrollLeft, 0));
        pageRangeEnd = client.getDateFromCoordinate(pageEndX - normalGridX + scrollLeft) || rangeEnd;
      }
    } else {
      pageRangeStart = rangeStart;
      pageRangeEnd = rangeEnd;
    }
    return {
      pageRangeStart,
      pageRangeEnd
    };
  }
  prepareExportElement() {
    const { element, exportMeta } = this, { id: id2, headerId, footerId, scrollLeft } = exportMeta.subGrids.normal, el = element.querySelector(`[id="${id2}"]`);
    el.querySelectorAll(".b-sch-canvas").forEach((canvasEl) => {
      canvasEl.style.height = "";
      if (scrollLeft) {
        canvasEl.style.marginInlineStart = `-${scrollLeft}px`;
      }
    });
    if (scrollLeft) {
      [headerId, footerId].forEach((id3) => {
        const el2 = element.querySelector(`[id="${id3}"] .b-widget-scroller`);
        if (el2) {
          el2.style.marginInlineStart = `-${scrollLeft}px`;
        }
      });
    }
    return super.prepareExportElement();
  }
  //#region Direct rendering
  renderTimeAxisHeaders(config, start, end) {
    const me = this, { exportMeta } = me, { client } = config, {
      isHorizontal,
      timeAxisHeaders
    } = exportMeta, timeAxisView = isHorizontal ? client.timeAxisColumn.timeAxisView : client.verticalTimeAxisColumn.view, domConfig = timeAxisView.buildCells(start, end), targetElement = document.createElement("div");
    DomSync.sync({
      targetElement,
      domConfig
    });
    DomHelper.forEachSelector(targetElement, ".b-sch-header-row", (headerRow, index) => {
      const headersMap = timeAxisHeaders[index];
      DomHelper.forEachSelector(headerRow, ".b-sch-header-timeaxis-cell", (el) => {
        if (!headersMap.has(el.dataset.tickIndex)) {
          headersMap.set(el.dataset.tickIndex, el.outerHTML);
        }
      });
    });
  }
  renderResourceHeaders(config) {
    const me = this, { client } = config;
    if (client.isHorizontal) {
      return;
    }
    const { exportMeta } = me, resourceHeader = client.timeAxisColumn.resourceColumns, children = resourceHeader.renderResourceHeaders(0, client.resourceStore.allResourceRecords.length - 1), targetElement = document.createElement("div");
    DomSync.sync({
      targetElement,
      domConfig: {
        onlyChildren: true,
        children
      }
    });
    exportMeta.resourceHeaders = targetElement.innerHTML;
  }
  renderEvents(config, rows, start, end) {
    const me = this, { client } = config, {
      isHorizontal,
      eventsMap,
      eventsBoxes,
      resourceTimeRangesMap
    } = me.exportMeta, wrapElement = DomHelper.createElement({
      parent: document.body,
      style: "visibility:hidden;",
      children: [{
        tag: "div"
      }]
    }), targetElement = wrapElement.firstChild;
    if (isHorizontal) {
      for (const row of rows) {
        const resource = client.store.getAt(row.dataIndex), resourceLayout = client.currentOrientation.getResourceLayout(resource), left = client.getCoordinateFromDate(start), right = client.getCoordinateFromDate(end), eventDOMConfigs = client.currentOrientation.getEventDOMConfigForCurrentView(
          resourceLayout,
          row,
          client.rtl ? right : left,
          client.rtl ? left : right
        );
        for (const domConfig of eventDOMConfigs) {
          const { eventId } = domConfig.dataset, { insetInlineStart, insetBlockStart, width, height } = domConfig.style;
          DomSync.sync({
            targetElement,
            domConfig,
            callback: ({ action, domConfig: domConfig2, lastDomConfig, targetElement: targetElement2, jsx }) => horizontalRenderingDomSyncCallback(client, false, { action, domConfig: domConfig2, lastDomConfig, targetElement: targetElement2, jsx })
          });
          const id2 = String(eventId);
          if (domConfig.elementData.entityName === "resourceTimeRange") {
            resourceTimeRangesMap.set(id2, targetElement.outerHTML);
          } else {
            eventsBoxes.set(id2, new Rectangle(insetInlineStart, insetBlockStart, width, height));
            eventsMap.set(id2, targetElement.outerHTML);
          }
        }
      }
    } else {
      client.resources.forEach((resource) => {
        const resourceEventsDomConfigs = client.vertical.renderResource(resource, start, end);
        for (const domConfig of resourceEventsDomConfigs) {
          const { eventId } = domConfig.dataset, {
            left,
            width,
            height,
            fontSize
          } = domConfig.style, top = domConfig.style.top;
          DomSync.sync({
            targetElement,
            domConfig,
            callback: ({ action, domConfig: domConfig2, lastDomConfig, targetElement: targetElement2, jsx }) => verticalRenderingDomSyncCallback(client, false, { action, domConfig: domConfig2, lastDomConfig, targetElement: targetElement2, jsx })
          });
          if (domConfig.elementData.renderData.entityName === "resourceTimeRange") {
            resourceTimeRangesMap.set(String(eventId), targetElement.outerHTML);
          } else {
            eventsMap.set(eventId, targetElement.outerHTML);
            eventsBoxes.set(String(eventId), new Rectangle(left, top, width, typeof height === "string" ? fontSize : height));
          }
        }
      });
    }
    wrapElement.remove();
  }
  renderLines(config, start, end) {
    const me = this, { client } = config, { exportMeta } = me, {
      columnLinesPlaceholder
    } = exportMeta;
    if (columnLinesPlaceholder) {
      const domConfigs = client.features.columnLines.getColumnLinesDOMConfig(
        start,
        end,
        0,
        client.resourceStore.allResourceRecords.length - 1
      ), targetElement = document.createElement("div");
      DomSync.sync({
        targetElement,
        domConfig: {
          onlyChildren: true,
          children: domConfigs
        }
      });
      exportMeta.columnLines = targetElement.innerHTML;
    }
  }
  renderRanges(config, start, end) {
    const me = this, { client } = config, { exportMeta } = me, { timeRanges } = exportMeta;
    if (timeRanges) {
      timeRanges.body = timeRanges.header = "";
      for (const feature of Object.values(client.features).filter((f) => f instanceof AbstractTimeRanges)) {
        const domConfigs = feature.getDOMConfig(start, end), targetElement = document.createElement("div");
        domConfigs.forEach((children, i) => {
          DomSync.sync({
            targetElement,
            domConfig: {
              children,
              onlyChildren: true
            }
          });
          if (i === 0) {
            timeRanges.body += targetElement.innerHTML;
          } else {
            timeRanges.header += targetElement.innerHTML;
          }
        });
      }
    }
  }
  //#endregion
  buildPageHtml() {
    const me = this, {
      subGrids,
      timeAxisHeaders,
      timeAxisPlaceholders,
      eventsMap,
      eventsPlaceholder,
      resourceTimeRangesMap,
      resourceHeaders,
      resourceHeaderPlaceholder,
      columnLines,
      columnLinesPlaceholder,
      timeRanges,
      timeRangesHeaderPlaceholder,
      timeRangesBodyPlaceholder,
      resourceTimeRangesPlaceholder,
      isHorizontal
    } = me.exportMeta;
    let html = me.prepareExportElement();
    if (isHorizontal) {
      Object.values(subGrids).forEach(({ placeHolder, rows, mergedCellsHtml }) => {
        const placeHolderText = placeHolder.outerHTML;
        let contentHtml = rows.join("");
        if (mergedCellsHtml == null ? void 0 : mergedCellsHtml.length) {
          contentHtml += `<div class="b-grid-merged-cells-container">${mergedCellsHtml.join("")}</div>`;
        }
        html = html.replace(placeHolderText, contentHtml);
      });
    }
    if (eventsPlaceholder) {
      html = html.replace(eventsPlaceholder.outerHTML, Array.from(eventsMap.values()).join(""));
    }
    if (resourceTimeRangesPlaceholder) {
      html = html.replace(resourceTimeRangesPlaceholder.outerHTML, Array.from(resourceTimeRangesMap.values()).join(""));
    }
    if (resourceHeaderPlaceholder) {
      html = html.replace(resourceHeaderPlaceholder.outerHTML, resourceHeaders);
    }
    timeAxisHeaders.forEach((headers, index) => {
      html = html.replace(timeAxisPlaceholders[index].outerHTML, Array.from(headers.values()).join(""));
    });
    if (columnLines) {
      html = html.replace(columnLinesPlaceholder.outerHTML, columnLines);
    }
    if (timeRanges && timeRangesBodyPlaceholder) {
      html = html.replace(timeRangesBodyPlaceholder.outerHTML, timeRanges.body);
      if (timeRangesHeaderPlaceholder) {
        html = html.replace(timeRangesHeaderPlaceholder.outerHTML, timeRanges.header);
      }
    }
    html = me.buildDependenciesHtml(html);
    return html;
  }
  getEventBox(event, dependency) {
    const me = this, {
      client,
      eventsBoxes,
      currentPageFirstRowIndex,
      exactGridHeight,
      totalHeight
    } = me.exportMeta, {
      resourceColumnWidth,
      rowHeight,
      isHorizontal,
      resourceStore
    } = client;
    let box = event && eventsBoxes.get(String(event.id));
    if (box && event.isMilestone) {
      box.translate(-box.width / 2, 0);
    }
    if (!box) {
      const bottomResourceIndex = event.assignments.reduce((result, { resource }) => {
        if (result === null) {
          result = resourceStore.indexOf(resource);
        } else {
          result = Math.max(result, resourceStore.indexOf(resource));
        }
        return result;
      }, null);
      if (isHorizontal) {
        if (bottomResourceIndex < currentPageFirstRowIndex) {
          box = new Rectangle(0, -rowHeight * 2, 10, rowHeight);
        } else {
          box = new Rectangle(0, exactGridHeight + rowHeight, 10, rowHeight);
        }
      } else {
        box = new Rectangle(
          bottomResourceIndex * resourceColumnWidth,
          totalHeight + rowHeight,
          resourceColumnWidth,
          rowHeight
        );
      }
    }
    return box;
  }
  renderDependencies() {
    const me = this, {
      client,
      eventsBoxes
    } = me.exportMeta, { dependencies } = client, dependencyFeature = client.features.dependencies, targetElement = DomHelper.createElement();
    let draw = false;
    dependencies.forEach((dependency) => {
      var _a5, _b2;
      if (!eventsBoxes.has(String((_a5 = dependency.fromEvent) == null ? void 0 : _a5.id)) && !eventsBoxes.has(String((_b2 = dependency.toEvent) == null ? void 0 : _b2.id)) || !dependencyFeature.isDependencyVisible(dependency)) {
        return;
      }
      const fromBox = me.getEventBox(dependency.fromEvent, dependency), toBox = me.getEventBox(dependency.toEvent, dependency);
      dependencyFeature.drawDependency(dependency, true, { from: fromBox == null ? void 0 : fromBox.clone(), to: toBox == null ? void 0 : toBox.clone() });
      draw = true;
    });
    if (draw) {
      dependencyFeature.domSync(targetElement, true);
    }
    return targetElement.innerHTML;
  }
  buildDependenciesHtml(html) {
    const { dependenciesPlaceholder, includeTimeline } = this.exportMeta;
    if (dependenciesPlaceholder && includeTimeline) {
      const placeholder = dependenciesPlaceholder.outerHTML;
      html = html.replace(placeholder, this.renderDependencies());
    }
    return html;
  }
};

// ../Scheduler/lib/Scheduler/feature/export/exporter/MultiPageExporter.js
var MultiPageExporter2 = class extends SchedulerExporterMixin_default(MultiPageExporter) {
  async stateNextPage(config) {
    await super.stateNextPage(config);
    this.exportMeta.eventsBoxes.clear();
  }
};
__publicField(MultiPageExporter2, "isHorizontal", true);
__publicField(MultiPageExporter2, "$name", "MultiPageExporter");
__publicField(MultiPageExporter2, "type", "multipage");
MultiPageExporter2._$name = "MultiPageExporter";

// ../Scheduler/lib/Scheduler/feature/export/exporter/MultiPageVerticalExporter.js
var MultiPageVerticalExporter2 = class extends SchedulerExporterMixin_default(MultiPageVerticalExporter) {
  async prepareComponent(config) {
    await super.prepareComponent(config);
    if (config.scheduleRange !== ScheduleRange.completeview) {
      this.estimateTotalPages(config);
    }
  }
};
__publicField(MultiPageVerticalExporter2, "isHorizontal", true);
__publicField(MultiPageVerticalExporter2, "$name", "MultiPageVerticalExporter");
__publicField(MultiPageVerticalExporter2, "type", "multipagevertical");
MultiPageVerticalExporter2._$name = "MultiPageVerticalExporter";

// ../Scheduler/lib/Scheduler/view/export/field/ScheduleRangeCombo.js
var ScheduleRangeCombo = class extends Combo {
  static get defaultConfig() {
    return {
      editable: false,
      localizeDisplayFields: true,
      displayField: "text",
      buildItems() {
        return Object.entries(ScheduleRange).map(([id2, text]) => ({ value: id2, text: "L{" + text + "}" }));
      }
    };
  }
};
__publicField(ScheduleRangeCombo, "$name", "ScheduleRangeCombo");
// Factoryable type name
__publicField(ScheduleRangeCombo, "type", "schedulerangecombo");
ScheduleRangeCombo.initClass();
ScheduleRangeCombo._$name = "ScheduleRangeCombo";

// ../Scheduler/lib/Scheduler/view/export/SchedulerExportDialog.js
var SchedulerExportDialog = class extends ExportDialog {
  static get configurable() {
    return {
      defaults: {
        localeClass: this
      },
      items: {
        scheduleRangeField: {
          type: "schedulerangecombo",
          label: "L{Schedule range}",
          name: "scheduleRange",
          value: "completeview",
          weight: 150,
          onChange({ value: value2 }) {
            this.parent.widgetMap.rangesContainer.hidden = value2 !== ScheduleRange.daterange;
          }
        },
        rangesContainer: {
          type: "container",
          flex: "1 0 100%",
          weight: 151,
          hidden: true,
          defaults: {
            localeClass: this
          },
          items: {
            filler: {
              // Filler widget to align date fields
              weight: 0,
              type: "widget",
              style: "margin-inline-end: -1em;"
            },
            rangeStartField: {
              type: "datefield",
              label: "L{Export from}",
              name: "rangeStart",
              labelWidth: "3em",
              flex: "1 0 25%",
              weight: 10,
              onChange({ value: value2 }) {
                this.parent.widgetMap.rangeEndField.min = value2 ? DateHelper.add(value2, 1, "d") : null;
              }
            },
            rangeEndField: {
              type: "datefield",
              label: "L{Export to}",
              name: "rangeEnd",
              labelWidth: "1em",
              flex: "1 0 25%",
              weight: 30,
              onChange({ value: value2 }) {
                this.parent.widgetMap.rangeStartField.max = value2 ? DateHelper.add(value2, -1, "d") : null;
              }
            }
          }
        }
      }
    };
  }
  //endregion
  onLocaleChange() {
    const labelWidth = this.L("L{labelWidth}");
    this.width = this.L("L{width}");
    this.items.forEach((widget) => {
      if (widget instanceof Field) {
        widget.labelWidth = labelWidth;
      } else if (widget.ref === "rangesContainer") {
        widget.items[0].width = labelWidth;
      }
    });
  }
  applyInitialValues(config) {
    super.applyInitialValues(config);
    const me = this, {
      client,
      scheduleRange
    } = config, items2 = config.items = config.items || {}, scheduleRangeField = items2.scheduleRangeField = items2.scheduleRangeField || {}, rangesContainer = items2.rangesContainer = items2.rangesContainer || {}, rangesContainerItems = rangesContainer.items = rangesContainer.items || {}, filler = rangesContainerItems.filler = rangesContainerItems.filler || {}, rangeStartField = rangesContainerItems.rangeStartField = rangesContainerItems.rangeStartField || {}, rangeEndField = rangesContainerItems.rangeEndField = rangesContainerItems.rangeEndField || {};
    filler.width = me.L("L{labelWidth}");
    scheduleRangeField.value = scheduleRangeField.value || scheduleRange;
    if (scheduleRangeField.value === ScheduleRange.daterange) {
      rangesContainer.hidden = false;
    }
    const rangeStart = rangeStartField.value = rangeStartField.value || client.startDate;
    rangeStartField.max = DateHelper.max(client.startDate, DateHelper.add(client.endDate, -1, "d"));
    let rangeEnd = rangeEndField.value || client.endDate;
    if (rangeEnd <= rangeStart) {
      rangeEnd = DateHelper.add(rangeStart, 1, "d");
    }
    rangeEndField.value = rangeEnd;
    rangeEndField.min = DateHelper.min(client.endDate, DateHelper.add(client.startDate, 1, "d"));
  }
};
//region Config
__publicField(SchedulerExportDialog, "$name", "SchedulerExportDialog");
__publicField(SchedulerExportDialog, "type", "schedulerexportdialog");
SchedulerExportDialog._$name = "SchedulerExportDialog";

// ../Scheduler/lib/Scheduler/feature/export/exporter/SinglePageExporter.js
var SinglePageExporter2 = class extends SchedulerExporterMixin_default(SinglePageExporter) {
};
__publicField(SinglePageExporter2, "isHorizontal", true);
__publicField(SinglePageExporter2, "$name", "SinglePageExporter");
__publicField(SinglePageExporter2, "type", "singlepage");
SinglePageExporter2._$name = "SinglePageExporter";

// ../Scheduler/lib/Scheduler/feature/export/verticalexporter/VerticalExporter.js
var VerticalExporter = class extends ExporterBase {
  cloneElement(element, target = this.element, clear = true) {
    super.cloneElement(element, target, clear);
  }
  async prepareComponent(config) {
    await super.prepareComponent(config);
    const {
      exportMeta,
      element
    } = this, { resourceColumns } = config.client.timeAxisColumn;
    exportMeta.resourceHeaderPlaceholder = this.createPlaceholder(element.querySelector(`#${resourceColumns.id}`));
    exportMeta.resourceHeaders = "";
  }
  async onRowsCollected() {
  }
};
__publicField(VerticalExporter, "isHorizontal", false);
VerticalExporter._$name = "VerticalExporter";

// ../Scheduler/lib/Scheduler/feature/export/verticalexporter/SinglePageExporterVertical.js
var SinglePageExporterVertical = class extends SchedulerExporterMixin_default(VerticalExporter) {
  static get title() {
    return this.localize("L{SinglePageExporter.singlepage}");
  }
  async prepareComponent(config) {
    await super.prepareComponent(config);
    Object.assign(this.exportMeta, {
      verticalPages: 1,
      horizontalPages: 1,
      totalPages: 1,
      currentPage: 0,
      verticalPosition: 0,
      horizontalPosition: 0
    });
  }
  async renderPage(config) {
    const { totalHeight } = this.exportMeta;
    await this.onRowsCollected([], config);
    this.exportMeta.exactGridHeight = totalHeight;
    return totalHeight;
  }
  buildPageHtml(config) {
    const { subGrids } = this.exportMeta;
    let html = super.buildPageHtml(config);
    Object.values(subGrids).forEach(({ placeHolder }) => {
      const placeHolderText = placeHolder.outerHTML, contentHtml = "";
      html = html.replace(placeHolderText, contentHtml);
    });
    return html;
  }
};
__publicField(SinglePageExporterVertical, "$name", "SinglePageExporterVertical");
__publicField(SinglePageExporterVertical, "type", "verticalsinglepage");
__publicField(SinglePageExporterVertical, "configurable", {
  /**
   * Set to `true` to center content horizontally on the page
   * @config {Boolean}
   */
  centerContentHorizontally: false
});
SinglePageExporterVertical.prototype.pagesExtractor = async function* pagesExtractor5(config) {
  const me = this, { client } = config, {
    totalWidth,
    verticalTimeAxisMargin
  } = me.exportMeta, styles2 = me.stylesheets, isPortrait = config.orientation === Orientation.portrait, paperFormat = PaperFormat[config.paperFormat], paperWidth = me.getPaperWidth(paperFormat, isPortrait), paperHeight = me.getPaperHeight(paperFormat, isPortrait);
  let totalHeight, header, footer;
  totalHeight = await me.renderPage(config);
  if (config.headerTpl) {
    header = me.prepareHTML(config.headerTpl({ totalWidth }));
    const height = me.measureElement(header, "b-export-header");
    totalHeight += height;
  }
  if (config.footerTpl) {
    footer = me.prepareHTML(config.footerTpl({ totalWidth }));
    const height = me.measureElement(footer, "b-export-footer");
    totalHeight += height;
  }
  const html = me.buildPageHtml(config), totalClientHeight = totalHeight, widthScale = Math.min(1, me.getScaleValue(me.inchToPx(paperWidth), totalWidth)), heightScale = Math.min(1, me.getScaleValue(me.inchToPx(paperHeight), totalHeight)), scale = Math.min(widthScale, heightScale);
  styles2.push(
    `<style>
                #${client.id} {
                    height: ${totalClientHeight}px !important;
                    width: ${totalWidth}px !important;
                }
                .b-verticaltimeaxiscolumn {
                    margin-top: ${verticalTimeAxisMargin}px;
                }
                .b-page-wrap {
                    width: ${paperWidth}in;
                    height: ${paperHeight}in;
                    ${me.centerContentHorizontally ? "display: flex; justify-content: center" : ""}
                }
                .b-print .b-export-content {
                    scale: ${scale};
                    transform-origin: top left;
                    height: calc(100% / ${scale});
                    width: calc(100% / ${scale});
                }
            </style>`
  );
  yield {
    html: me.pageTpl({
      html,
      header,
      footer,
      styles: styles2,
      paperWidth,
      paperHeight,
      currentPage: 0
    })
  };
};
SinglePageExporterVertical._$name = "SinglePageExporterVertical";

// ../Scheduler/lib/Scheduler/feature/export/verticalexporter/MultiPageVerticalExporterVertical.js
var MultiPageVerticalExporterVertical = class extends SchedulerExporterMixin_default(VerticalExporter) {
  static get title() {
    return this.localize("L{MultiPageVerticalExporter.multipagevertical}");
  }
  calculateScale() {
    const { exportMeta } = this;
    return exportMeta.scale = this.getScaleValue(exportMeta.pageWidth, exportMeta.totalWidth);
  }
  estimateTotalPages(config) {
    var _a5;
    const me = this, { exportMeta } = me, {
      client,
      headerTpl,
      footerTpl,
      repeatHeader,
      rangeStart,
      rangeEnd
    } = config, { pageHeight } = exportMeta, { timeAxisViewModel } = client, timeRangeDistance = timeAxisViewModel.getDistanceBetweenDates(rangeStart, rangeEnd), msPerPixel = (rangeEnd - rangeStart) / timeRangeDistance;
    const totalWidth = exportMeta.totalWidth = client.timeAxisColumn.resourceColumns.totalWidth + client.subGrids.locked.width + ((_a5 = client.subGrids.locked.splitterElement) == null ? void 0 : _a5.offsetWidth) || 0, scale = me.calculateScale();
    let totalHeight = exportMeta.totalHeight = timeRangeDistance + client.headerHeight + client.footerHeight, contentHeight = pageHeight / scale;
    if (headerTpl) {
      contentHeight -= me.measureElement(headerTpl({
        totalWidth,
        totalPages: -1,
        currentPage: -1
      }), "b-export-header");
    }
    if (footerTpl) {
      contentHeight -= me.measureElement(footerTpl({
        totalWidth,
        totalPages: -1,
        currentPage: -1
      }), "b-export-footer");
    }
    if (repeatHeader) {
      totalHeight -= client.headerHeight;
    }
    let verticalPages = Math.ceil(totalHeight / contentHeight);
    if (repeatHeader) {
      totalHeight += client.headerHeight * verticalPages;
      verticalPages = Math.ceil(totalHeight / contentHeight);
    }
    Object.assign(exportMeta, {
      scale,
      contentHeight,
      totalWidth,
      totalHeight,
      verticalPages,
      msPerPixel,
      initialScroll: 0,
      horizontalPages: 1,
      totalPages: verticalPages,
      currentPage: 0,
      firstTickTop: 0
    });
  }
  async stateNextPage(config) {
    ++this.exportMeta.currentPage;
    ++this.exportMeta.verticalPosition;
    await super.stateNextPage(config);
  }
  async prepareComponent(config) {
    await super.prepareComponent(config);
    this.estimateTotalPages(config);
    this.exportMeta.pageRangeStart = config.rangeStart;
    this.exportMeta.exactGridHeight = this.exportMeta.totalHeight;
  }
  async renderRows(config) {
    const me = this, { exportMeta } = me, { client } = config, {
      rangeEnd,
      repeatHeader
    } = config, {
      currentPage,
      contentHeight,
      msPerPixel,
      pageRangeStart
    } = exportMeta;
    let remainingHeight = contentHeight;
    if (currentPage === 0 || repeatHeader) {
      remainingHeight -= client.headerHeight;
    }
    const currentPageDuration = remainingHeight * msPerPixel;
    exportMeta.firstTickTop = client.timeAxisViewModel.getPositionFromDate(pageRangeStart);
    exportMeta.pageRangeEnd = DateHelper.min(new Date(pageRangeStart.getTime() + currentPageDuration), rangeEnd);
    await me.onRowsCollected([], config);
    if (exportMeta.pageRangeEnd.getTime() < rangeEnd.getTime()) {
      const tickIndex = Math.floor(client.timeAxis.getTickFromDate(exportMeta.pageRangeEnd, true));
      exportMeta.pageRangeStart = client.timeAxis.getAt(tickIndex).startDate;
    }
  }
  getCurrentPageDateRange() {
    return {
      pageRangeStart: this.exportMeta.pageRangeStart,
      pageRangeEnd: this.exportMeta.pageRangeEnd
    };
  }
  async buildPage(config) {
    const me = this, { exportMeta } = me, {
      headerTpl,
      footerTpl
    } = config, {
      totalWidth,
      totalPages,
      currentPage,
      subGrids
    } = exportMeta;
    Object.values(subGrids).forEach((subGrid) => subGrid.rows = []);
    let header, footer;
    if (headerTpl) {
      header = me.prepareHTML(headerTpl({
        totalWidth,
        totalPages,
        currentPage
      }));
    }
    if (footerTpl) {
      footer = me.prepareHTML(footerTpl({
        totalWidth,
        totalPages,
        currentPage
      }));
    }
    await me.renderRows(config);
    const html = me.buildPageHtml(config);
    return { html, header, footer };
  }
};
__publicField(MultiPageVerticalExporterVertical, "$name", "MultiPageVerticalExporterVertical");
__publicField(MultiPageVerticalExporterVertical, "type", "verticalmultipagevertical");
MultiPageVerticalExporterVertical.prototype.pagesExtractor = async function* pagesExtractor6(config) {
  const me = this, {
    exportMeta,
    stylesheets
  } = me, {
    totalPages,
    totalWidth,
    paperWidth,
    paperHeight,
    realPaperWidth,
    realPaperHeight,
    title
  } = exportMeta;
  let currentPage, style;
  while ((currentPage = exportMeta.currentPage) < totalPages) {
    me.trigger("exportStep", {
      text: MultiPageVerticalExporter2.L(MultiPageVerticalExporter2.exportingPageText, { currentPage, totalPages }),
      progress: Math.round((currentPage + 1) / totalPages * 90)
    });
    const { html, header, footer } = await me.buildPage(config), {
      scale,
      firstTickTop
    } = me.exportMeta;
    style = `
            .b-page-${currentPage} .b-grid-vertical-scroller {
                margin-top: -${firstTickTop}px
            }
            .b-page-${currentPage} #${config.client.id} {
                height: ${exportMeta.exactGridHeight}px !important;
                width: ${totalWidth}px !important;
            }
            .b-page-wrap {
                width: ${realPaperWidth}in;
                height: ${realPaperHeight}in;
            }
            .b-print .b-export-viewport {
                scale: ${scale};
                transform-origin: top left;
                height: calc(100% / ${scale});
                width: calc(100% / ${scale});
            }
        `;
    if (config.repeatHeader) {
      style = `
                ${style}
                .b-export-body {
                    height: 100%;
                    display: flex;
                }
                .b-export-viewport {
                    height: 100%;
                }
            `;
    } else {
      style = `
                ${style}
                ${currentPage === 0 ? "" : `.b-page-${currentPage} .b-grid-header-container {
                    display: none;
                }`}
                .b-export-body {
                    overflow: hidden;
                }
            `;
    }
    const styles2 = [
      ...stylesheets,
      `<style>${style}</style>`
    ];
    await me.stateNextPage(config);
    yield {
      html: me.pageTpl({
        html,
        title,
        header,
        footer,
        styles: styles2,
        paperWidth,
        paperHeight,
        realPaperWidth,
        realPaperHeight,
        currentPage
      })
    };
  }
};
MultiPageVerticalExporterVertical._$name = "MultiPageVerticalExporterVertical";

// ../Scheduler/lib/Scheduler/feature/export/PdfExport.js
var PdfExport2 = class extends PdfExport {
  static get defaultConfig() {
    return {
      exporters: [
        SinglePageExporter2,
        MultiPageExporter2,
        MultiPageVerticalExporter2,
        SinglePageExporterVertical,
        MultiPageVerticalExporterVertical
      ],
      dialogClass: SchedulerExportDialog,
      /**
       * Specifies how to export time span.
       *  * completeview - Complete configured time span, from scheduler start date to end date
       *  * currentview  - Currently visible time span
       *  * daterange    - Use specific date range, provided additionally in config. See {@link #config-rangeStart}/
       *  {@link #config-rangeEnd}
       * @config {'completeview'|'currentview'|'daterange'}
       * @default
       * @category Export file config
       */
      scheduleRange: "completeview",
      /**
       * Exported time span range start. Used with `daterange` config of the {@link #config-scheduleRange}
       * @config {Date}
       * @category Export file config
       */
      rangeStart: null,
      /**
       * Returns the instantiated export dialog widget as configured by {@link #config-exportDialog}
       * @member {Scheduler.view.export.SchedulerExportDialog} exportDialog
       */
      /**
       * A config object to apply to the {@link Scheduler.view.export.SchedulerExportDialog} widget.
       * @config {SchedulerExportDialogConfig} exportDialog
       */
      /**
       * Exported time span range end. Used with `daterange` config of the {@link #config-scheduleRange}
       * @config {Date}
       * @category Export file config
       */
      rangeEnd: null
    };
  }
  get defaultExportDialogConfig() {
    const config = ObjectHelper.copyProperties(super.defaultExportDialogConfig, this, ["scheduleRange"]), { isHorizontal } = this.client;
    if (!isHorizontal) {
      config.items.exporterTypeField.value = "verticalsinglepage";
    }
    ObjectHelper.merge(config, {
      items: {
        columnsField: {
          hidden: !isHorizontal
        },
        rowsRangeField: {
          hidden: !isHorizontal
        },
        alignRowsField: {
          hidden: !isHorizontal
        }
      }
    });
    config.exporters = config.exporters.filter((exporter) => exporter.isHorizontal === isHorizontal);
    return config;
  }
  buildExportConfig(config) {
    config = super.buildExportConfig(config);
    const {
      scheduleRange,
      rangeStart,
      rangeEnd
    } = this;
    if (config.columns && !config.columns.find((col) => col.type === "timeAxis")) {
      config.columns.push(config.client.timeAxisColumn.id);
    }
    return ObjectHelper.assign({
      scheduleRange,
      rangeStart,
      rangeEnd
    }, config);
  }
};
__publicField(PdfExport2, "$name", "PdfExport");
PdfExport2._$name = "PdfExport";
GridFeatureManager.registerFeature(PdfExport2, false, "Scheduler");

// ../Scheduler/lib/Scheduler/feature/export/Print.js
var Print2 = class extends PrintMixin_default(PdfExport2) {
};
/**
 * @hideConfigs clientURL, exportServer, fetchOptions, fileFormat, fileName, openAfterExport, openInNewTab, sendAsBinary
 */
/**
 * @hideFunctions processExportContent, receiveExportContent, showExportDialog
 */
__publicField(Print2, "$name", "Print");
Print2._$name = "Print";
GridFeatureManager.registerFeature(Print2, false, "Scheduler");

// ../Scheduler/lib/Scheduler/util/ScheduleTableExporter.js
var ScheduleTableExporter = class extends Localizable_default(TableExporter) {
  construct(config = {}, ...args) {
    super.construct(config, ...args);
    if (!this.eventSortFn) {
      this.eventSortFn = (a, b) => a.internalId - b.internalId;
    }
  }
  normalizeColumns(config) {
    config.columns = config.resourceColumns || config.columns;
    super.normalizeColumns(config);
    config.columns = config.columns.filter((col) => !col.isVerticalTimeAxisColumn);
    config.eventColumns = config.eventColumns.map((col) => {
      if (typeof col === "string") {
        return { field: col };
      }
      return col;
    });
  }
  generateExportData(config) {
    const me = this, resourceColumns = me.generateResourceColumns(config), eventColumns = me.target.eventStore ? me.generateEventColumns(config) : [], columns = resourceColumns.concat(eventColumns), rows = me.generateRows(config);
    return { columns, rows };
  }
  generateEventColumns(config) {
    return config.eventColumns.map((column) => this.processEventColumn(column, config));
  }
  generateResourceColumns(config) {
    const client = this.target;
    if (client instanceof SchedulerBase && client.isHorizontal) {
      return super.generateColumns(config);
    }
    return config.columns.map((column) => this.processResourceColumn(column, config));
  }
  processEventColumn(column, config) {
    const { width, minWidth } = column, { defaultColumnWidth } = config;
    return {
      field: column.field,
      value: column.text,
      width: Math.max(width || defaultColumnWidth, minWidth || defaultColumnWidth),
      eventColumn: true,
      type: this.getColumnType(column, this.target.eventStore)
    };
  }
  processResourceColumn(column, config) {
    const { width, minWidth } = column, { defaultColumnWidth } = config;
    return {
      field: column.field,
      value: column.text,
      width: Math.max(width || defaultColumnWidth, minWidth || defaultColumnWidth),
      type: this.getColumnType(column, this.target.resourceStore)
    };
  }
  generateRows(config) {
    const me = this, { target } = me;
    let result;
    if (!target.resourceStore || target.isGanttBase) {
      result = super.generateRows(config);
    } else {
      result = [];
      target.resourceStore.map((resourceRecord) => {
        const events = resourceRecord.events || [];
        events.sort(me.eventSortFn);
        if (target.isSchedulerBase && !events.length) {
          events.push("");
        }
        events.forEach((eventRecord) => {
          if (eventRecord === "" || target.eventStore.isAvailable(eventRecord) && (config.includeEventsOutsideTimeAxis || eventRecord.isScheduled && target.intersectsVisibleTimeSpan(eventRecord))) {
            result.push(me.getRowData(config, resourceRecord, eventRecord));
          }
        });
      });
      if (config.includeUnassigned && config.eventColumns.length) {
        const notAssignedEvents = target.eventStore.query((eventRecord) => !eventRecord.resources.length);
        if (notAssignedEvents.length) {
          const cells = new Array(config.columns.length).fill("");
          cells.push(me.L("L{ExcelExporter.No resource assigned}"));
          result.push(cells);
          notAssignedEvents.forEach((eventRecord) => result.push(me.getRowData(config, null, eventRecord)));
        }
      }
      result = result.filter((cells) => cells.length);
    }
    return result;
  }
  getRowData(config, resource, event) {
    const {
      columns,
      eventColumns
    } = config, cells = [], resourceCells = this.processRecord(resource, columns, config);
    resourceCells && cells.push(...resourceCells);
    if (!resource || !resource.isSpecialRow) {
      const eventCells = this.processRecord(event, eventColumns, config);
      eventCells && cells.push(...eventCells);
    }
    return cells;
  }
};
__publicField(ScheduleTableExporter, "configurable", {
  /**
   * Set to `false` to not include unassigned events in the export. `true` by default.
   * @config {Boolean} includeUnassigned
   * @default
   */
  includeUnassigned: true,
  /**
   * Set to `true` to include events that do not intersect the span of the current time axis.
   * @config {Boolean} includeEventsOutsideTimeAxis
   */
  includeEventsOutsideTimeAxis: null,
  /**
   * An array of Event columns configuration used to specify columns width, headers name, and column fields to
   * get the data from.
   * 'field' config is required. If 'text' is missing, the 'field' config will be used instead.
   *
   * For example:
   * ```javascript
   * eventColumns    : [
   *     { text : 'Task', field : 'name' },
   *     { text : 'Starts', field : 'startDate', width : 140 },
   *     { text : 'Ends', field : 'endDate', width : 140 }
   * ]
   * ```
   *
   * @config {String[]|Object[]} eventColumns
   * @default
   */
  eventColumns: [
    { text: "Task", field: "name" },
    { text: "Starts", field: "startDate", width: 140, type: "date" },
    { text: "Ends", field: "endDate", width: 140, type: "date" }
  ],
  /**
   * An array of resource column configuration objects used to specify column widths, header text, and data fields to get the data from.
   * 'field' config is required. If 'text' is missing, it will read it from the grid column or the 'field' config.
   * If 'width' is missing, it will try to get it retrieved from the grid column or {@link #config-defaultColumnWidth} config.
   * If no columns provided the config will be generated from the scheduler columns (in horizontal mode).
   *
   * For example:
   * ```javascript
   * resourceColumns : [
   *     'firstName', // field
   *     { text : 'Role', field : 'role', width : 140 }
   * ]
   * ```
   *
   * @config {String[]|Object[]}
   */
  resourceColumns: null,
  /**
   * Function to sort events for each resource. By default, events are sorted in the order of appending to the
   * store.
   * For example:
   * ```javascript
   * // Sorting by start date
   * eventSortFn : (a, b) => a.startDate - b.startDate
   * ```
   * @config {Function}
   * @internal
   */
  eventSortFn: null
});
ScheduleTableExporter._$name = "ScheduleTableExporter";

// ../Scheduler/lib/Scheduler/feature/experimental/ExcelExporter.js
var ExcelExporter2 = class extends ExcelExporter {
};
__publicField(ExcelExporter2, "$name", "ExcelExporter");
__publicField(ExcelExporter2, "configurable", {
  /**
   * Exporter class. Must subclass {@link Scheduler.util.ScheduleTableExporter}
   * @config {Scheduler.util.ScheduleTableExporter}
   * @typings {typeof ScheduleTableExporter}
   * @default
   */
  exporterClass: ScheduleTableExporter,
  /**
   * Configuration object for {@link #config-exporterClass exporter class}.
   * @config {Object}
   */
  exporterConfig: null
});
ExcelExporter2._$name = "ExcelExporter";
GridFeatureManager.registerFeature(ExcelExporter2, false, ["Scheduler"]);

// ../Scheduler/lib/Scheduler/view/Scheduler.js
var Scheduler = class extends SchedulerBase {
};
__publicField(Scheduler, "$name", "Scheduler");
// Factoryable type name
__publicField(Scheduler, "type", "scheduler");
Scheduler.initClass();
Scheduler._$name = "Scheduler";

// ../Scheduler/lib/Scheduler/view/mixin/DelayedRecordsRendering.js
var DelayedRecordsRendering_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    static get properties() {
      return {
        recordsToRefresh: /* @__PURE__ */ new Set()
      };
    }
    beforeRenderRow({ record }) {
      var _a6;
      if ((_a6 = this.recordIsReadyForRendering) == null ? void 0 : _a6.call(this, record)) {
        this.unscheduleRecordRefresh(record);
      }
      return super.beforeRenderRow(...arguments);
    }
    cleanupScheduledRecord() {
      const { rowManager, store } = this;
      for (const record of [...this.recordsToRefresh]) {
        if (!record.stores.includes(store) || !rowManager.getRowById(record)) {
          this.recordsToRefresh.delete(record);
        }
      }
    }
    renderScheduledRecords() {
      const me = this;
      if (!me.refreshSuspended) {
        me.cleanupScheduledRecord();
        const { rowManager } = me, records = [...me.recordsToRefresh], rows = records.map((record) => rowManager.getRowById(record));
        if (rows.length) {
          rowManager.renderRows(rows);
          me.trigger("scheduledRecordsRender", { records, rows });
        }
        if (me.recordsToRefresh.size) {
          me.scheduleRecordRefresh();
        }
      } else {
        me.scheduleRecordRefresh();
      }
    }
    /**
     * Cancels scheduled rows refresh.
     * @param {Core.data.Model|Core.data.Model[]|Boolean} [clearRecords=true] `true` to also clear the list of records
     * scheduled for refreshing. `false` will result only canceling the scheduled call and keeping intact
     * the list of records planned for refreshing.
     */
    unscheduleRecordRefresh(clearRecords = true) {
      const me = this;
      if (clearRecords === true) {
        me.recordsToRefresh.clear();
      } else if (clearRecords) {
        ArrayHelper.asArray(clearRecords).forEach((record) => me.recordsToRefresh.delete(record));
      }
      if (me.scheduledRecordsRefreshTimer && !me.recordsToRefresh.size) {
        me.clearTimeout(me.scheduledRecordsRefreshTimer);
      }
    }
    /**
     * Schedules the provided record row refresh.
     * @param {Core.data.Model} records Record to refresh the row of.
     */
    scheduleRecordRefresh(records) {
      const me = this;
      if (records) {
        ArrayHelper.asArray(records).forEach((record) => me.recordsToRefresh.add(record));
      }
      me.scheduledRecordsRefreshTimer = me.setTimeout({
        fn: "renderScheduledRecords",
        delay: me.scheduledRecordsRefreshTimeout,
        cancelOutstanding: true
      });
    }
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "DelayedRecordsRendering"), __publicField(_a5, "configurable", {
    scheduledRecordsRefreshTimeout: 15
  }), _a5;
};

// ../Scheduler/lib/Scheduler/view/TimelineHistogramRendering.js
var TimelineHistogramRendering = class extends Base {
  construct(client) {
    super.construct();
    this.client = client;
  }
  init() {
  }
  onTimeAxisViewModelUpdate() {
    const { scrollable } = this.client.timeAxisSubGrid;
    this.updateFromHorizontalScroll(scrollable.x);
  }
  // Update header range on horizontal scroll
  updateFromHorizontalScroll(scrollX) {
    const me = this, {
      client,
      // scrollBuffer is an export only thing
      scrollBuffer
    } = me, {
      timeAxisSubGrid,
      timeAxis,
      rtl
    } = client, { width } = timeAxisSubGrid, { totalSize } = client.timeAxisViewModel, start = scrollX, returnEnd = timeAxisSubGrid.scrollable.maxX !== 0 && Math.abs(timeAxisSubGrid.scrollable.maxX) <= Math.round(start) + 5, startDate = client.getDateFromVirtualCoord({ coord: Math.max(0, start - scrollBuffer) }), endDate = returnEnd ? timeAxis.endDate : client.getDateFromVirtualCoord({ coord: start + width + scrollBuffer }) || timeAxis.endDate;
    if (startDate && !client._viewPresetChanging) {
      me._visibleDateRange = { startDate, endDate, startMS: startDate.getTime(), endMS: endDate.getTime() };
      me.viewportCoords = rtl ? { left: totalSize - scrollX - width + scrollBuffer, right: totalSize - scrollX - scrollBuffer } : { left: scrollX - scrollBuffer, right: scrollX + width + scrollBuffer };
      const range = client.timeView.range = { startDate, endDate };
      client.internalOnVisibleDateRangeChange(range);
      if (!client.refreshSuspended && client.rowManager.rows.length) {
        if (client.rowManager.rows[0].id === null) {
          return;
        }
        if (me._timeAxisStartDate - timeAxis.startDate || me._timeAxisEndDate - timeAxis.endDate) {
          me._timeAxisStartDate = timeAxis.startDate;
          me._timeAxisEndDate = timeAxis.endDate;
          client.rowManager.renderRows(client.rowManager.rows);
        }
      }
    }
  }
  onViewportResize() {
  }
  refreshRows() {
  }
  get visibleDateRange() {
    return this._visibleDateRange;
  }
  translateToPageCoordinate(x) {
    const { client } = this, { scrollable } = client.timeAxisSubGrid;
    let result = x + client.timeAxisSubGridElement.getBoundingClientRect().left;
    if (client.rtl) {
      result -= scrollable.maxX - Math.abs(client.scrollLeft);
    } else {
      result -= client.scrollLeft;
    }
    return result;
  }
  translateToScheduleCoordinate(x) {
    const { client } = this, { scrollable } = client.timeAxisSubGrid;
    let result = x - client.timeAxisSubGridElement.getBoundingClientRect().left - globalThis.scrollX;
    if (client.rtl) {
      result += scrollable.maxX - Math.abs(client.scrollLeft);
    } else {
      result += client.scrollLeft;
    }
    return result;
  }
  getDateFromXY(xy2, roundingMethod, local, allowOutOfRange = false) {
    const { client } = this;
    let coord = xy2[0];
    if (!local) {
      coord = this.translateToScheduleCoordinate(coord);
    }
    coord = client.getRtlX(coord);
    return client.timeAxisViewModel.getDateFromPosition(coord, roundingMethod, allowOutOfRange);
  }
};
__publicField(TimelineHistogramRendering, "configurable", {
  scrollBuffer: 0
});
TimelineHistogramRendering._$name = "TimelineHistogramRendering";

// ../Scheduler/lib/Scheduler/view/TimelineHistogramBase.js
var histogramWidgetCleanState = {
  series: null,
  topValue: null
};
var emptyFn4 = () => {
};
var TimelineHistogramBase = class extends TimelineBase.mixin(DelayedRecordsRendering_default) {
  static get properties() {
    return {
      histogramDataByRecord: /* @__PURE__ */ new Map(),
      collectingDataFor: /* @__PURE__ */ new Map(),
      _refreshRequested: 0
    };
  }
  updateGetRecordData(fn2) {
    this._getRecordData = fn2 ? this.resolveCallback(fn2) : null;
  }
  updateHardRefreshOnTimeAxisReconfigure(value2) {
    const name = "hardRefreshOnTimeAxisReconfigure";
    if (value2) {
      this.timeAxis.ion({
        name,
        endReconfigure: "onTimeAxisEndReconfigure",
        thisObj: this
      });
    } else {
      this.detachListeners(name);
    }
  }
  //endregion
  //region Constructor/Destructor
  construct(config) {
    super.construct(config);
    const me = this;
    me.scheduleRefreshRows = me.createOnFrame(me.refreshRows, [], me, true);
    me.rowManager.ion({
      beforeRowHeight: "onBeforeRowHeight",
      thisObj: me
    });
  }
  onDestroy() {
    var _a5;
    this.clearHistogramDataCache();
    (_a5 = this._histogramWidget) == null ? void 0 : _a5.destroy();
    this.barTooltip = null;
  }
  //endregion
  //region Internal
  // Used by shared features to resolve an event or task
  resolveTimeSpanRecord(element) {
  }
  getScheduleMouseEventParams(cellData, event) {
    const record = this.store.getById(cellData.id);
    return { record };
  }
  get currentOrientation() {
    if (!this._currentOrientation) {
      this._currentOrientation = new TimelineHistogramRendering(this);
    }
    return this._currentOrientation;
  }
  updateSeries(value2) {
    const me = this;
    me.histogramWidget.series = value2;
    me._series = me.histogramWidget.series;
    if (me.isPainted && !me.isConfiguring) {
      me.scheduleRefreshRows();
    }
  }
  getAsyncEventSuffixForStore(store) {
    return store.isAbstractPartOfProjectStoreMixin ? "PreCommit" : "";
  }
  /**
   * Schedules the component rows refresh on the next animation frame. However many time it is
   * called in one event run, it will only be scheduled to run once.
   */
  scheduleRefreshRows() {
  }
  refreshRows(...args) {
    if (this.refreshSuspended) {
      this._refreshRequested++;
    } else {
      this._refreshRequested = 0;
    }
    return super.refreshRows(...args);
  }
  getRowHeight() {
    return this.rowHeight;
  }
  onInternalPaint({ firstPaint }) {
    super.onInternalPaint({ firstPaint });
    if (firstPaint && this.showBarTip) {
      this.barTooltip = {};
    }
  }
  changeBarTooltip(tooltip, oldTooltip) {
    oldTooltip == null ? void 0 : oldTooltip.destroy();
    if (tooltip) {
      return tooltip.isTooltip ? tooltip : this.barTooltipClass.new({
        forElement: this.timeAxisSubGridElement,
        forSelector: ".b-histogram rect",
        hoverDelay: 0,
        trackMouse: false,
        cls: "b-celltooltip-tip",
        getHtml: this.getTipHtml.bind(this)
      }, this.showBarTip, tooltip);
    }
    return null;
  }
  async getTipHtml(args) {
    if (this.showBarTip && this.barTooltipTemplate) {
      const { activeTarget } = args, index = parseInt(activeTarget.dataset.index, 10), record = this.getRecordFromElement(activeTarget), histogramData = await this.getRecordHistogramData(record);
      return this.barTooltipTemplate({
        ...args,
        datum: this.extractHistogramDataArray(histogramData, record)[index],
        record,
        index
      });
    }
  }
  collectTicksWidth() {
    const { ticks } = this.timeAxis, prevDuration = ticks[0].endDate - ticks[0].startDate, tickDurations = { 0: prevDuration };
    let totalDuration = prevDuration, isMonotonous = true;
    for (let i = 1, { length } = ticks; i < length; i++) {
      const tick = ticks[i], duration = tick.endDate - tick.startDate;
      if (prevDuration !== duration) {
        isMonotonous = false;
      }
      totalDuration += duration;
      tickDurations[i] = duration;
    }
    if (!isMonotonous) {
      const ticksWidth = {};
      for (let i = 0, { length } = ticks; i < length; i++) {
        ticksWidth[i] = tickDurations[i] / totalDuration;
      }
      this.ticksWidth = ticksWidth;
    } else {
      this.ticksWidth = null;
    }
  }
  changeHistogramWidget(widget) {
    var _a5;
    const me = this;
    if (widget && !widget.isHistogram) {
      if (me.getBarTextDOMConfig && !widget.getBarTextDOMConfig) {
        widget.getBarTextDOMConfig = me.getBarTextDOMConfig;
      }
      widget = me.histogramWidgetClass.new({
        owner: me,
        appendTo: me.element,
        height: me.rowHeight,
        width: ((_a5 = me.timeAxisColumn) == null ? void 0 : _a5.width) || 0,
        getBarTip: emptyFn4,
        getBarClass: me.getBarClass || me.getBarClassDefault,
        getBarText: me.getBarText || me.getBarTextDefault,
        getBarDOMConfig: me.getBarDOMConfig,
        getOutlineClass: me.getOutlineClass,
        getOutlineDOMConfig: me.getOutlineDOMConfig
      }, widget);
      widget.suspendRefresh();
      me.getBarTextDefault = me.getBarTextDefault.bind(widget);
    }
    return widget;
  }
  // Injectable method.
  getBarClassDefault(series, domConfig, datum) {
  }
  getBarTextDefault(datum, index) {
  }
  updateShowBarTip(value2) {
    this.barTooltip = value2;
  }
  //endregion
  //region Events
  onHistogramDataCacheSet({ record, data }) {
    this.scheduleRecordRefresh(record);
  }
  onTimeAxisEndReconfigure() {
    if (this.hardRefreshOnTimeAxisReconfigure) {
      this.clearHistogramDataCache();
      this.scheduleRefreshRows();
    }
  }
  onStoreUpdateRecord({ record, changes }) {
    const me = this;
    if (!me.getRecordData && me.dataModelField && changes[me.dataModelField]) {
      me.clearHistogramDataCache(record);
    }
    return super.onStoreUpdateRecord(...arguments);
  }
  onStoreRemove({ records }) {
    super.onStoreRemove(...arguments);
    for (const record of records) {
      this.clearHistogramDataCache(record);
    }
  }
  onBeforeRowHeight({ height }) {
    if (this._timeAxisColumn) {
      const widget = this._histogramWidget;
      if (widget) {
        widget.height = height;
        widget.onElementResize(widget.element);
      }
    }
  }
  onTimeAxisViewModelUpdate() {
    super.onTimeAxisViewModelUpdate(...arguments);
    const widget = this._histogramWidget;
    if (widget) {
      widget.width = this.timeAxisViewModel.totalSize;
      widget.onElementResize(widget.element);
    }
    this.collectTicksWidth();
  }
  //endregion
  //region Data processing
  extractHistogramDataArray(histogramData, record) {
    return histogramData;
  }
  processRecordRenderData(renderData) {
    return renderData;
  }
  /**
   * Clears the histogram data cache for the provided record (if provided).
   * If the record is not provided clears the cache for all records.
   * @param {Core.data.Model} [record] Record to clear the cache for.
   */
  clearHistogramDataCache(record) {
    const records = ArrayHelper.asArray(record);
    if (records) {
      for (const record2 of records) {
        this.histogramDataByRecord.delete(record2);
      }
    } else {
      this.histogramDataByRecord.clear();
    }
  }
  /**
   * Caches the provided histogram data for the given record.
   * @param {Core.data.Model} record Record to cache data for.
   * @param {Object} data Histogram data to cache.
   */
  setHistogramDataCache(record, data) {
    const eventData = { record, data };
    this.trigger("beforeHistogramDataCacheSet", eventData);
    this.histogramDataByRecord.set(eventData.record, eventData.data);
    this.trigger("histogramDataCacheSet", eventData);
  }
  /**
   * Returns entire histogram data cache if no record provided,
   * or cached data for the provided record.
   * @param {Core.data.Model} [record] Record to get the cached data for.
   * @returns {Object} The provided record cached data or all the records data cache
   * as a `Map` keyed by records.
   */
  getHistogramDataCache(record) {
    return record ? this.histogramDataByRecord.get(record) : this.histogramDataByRecord;
  }
  /**
   * Returns `true` if there is cached histogram data for the provided record.
   * @param {Core.data.Model} record Record to check the cache existence for.
   * @returns {Boolean} `True` if there is a cache for provided record.
   */
  hasHistogramDataCache(record) {
    return this.histogramDataByRecord.has(record);
  }
  finalizeDataRetrievingInternal(record, data) {
    this.collectingDataFor.delete(record);
    this.setHistogramDataCache(record, data);
    return data;
  }
  finalizeDataRetrieving(record, data) {
    if (Objects.isPromise(data)) {
      this.collectingDataFor.set(record, data);
      return data.then((data2) => {
        var _a5;
        return (_a5 = this.finalizeDataRetrievingInternal) == null ? void 0 : _a5.call(this, record, data2);
      });
    }
    return this.finalizeDataRetrievingInternal(record, data);
  }
  /**
   * Retrieves the histogram data for the provided record.
   *
   * The method first checks if there is cached data for the record and returns it if found.
   * Otherwise it starts collecting data by calling {@link #config-getRecordData} (if provided)
   * or by reading it from {@link #config-dataModelField} record field.
   *
   * The method can be asynchronous depending on the provided {@link #config-getRecordData} function.
   * If the function returns a `Promise` then the method will return a wrapping `Promise` in turn that will
   * resolve with the collected histogram data.
   *
   * The method triggers {@link #event-histogramDataCacheSet} event when a record data is ready.
   *
   * @param {Core.data.Model} record Record to retrieve the histogram data for.
   * @returns {Object|Promise} The histogram data for the provided record or a `Promise` that will provide the data
   * when resolved.
   */
  getRecordHistogramData(record) {
    const me = this, { getRecordData } = me;
    let result = me.collectingDataFor.get(record) || me.getHistogramDataCache(record);
    if (!result && !me.hasHistogramDataCache(record)) {
      if (getRecordData) {
        result = getRecordData.handler.call(getRecordData.thisObj, ...arguments);
      } else {
        result = record.get(me.dataModelField);
      }
      result = me.finalizeDataRetrieving(record, result);
    }
    return result;
  }
  recordIsReadyForRendering(record) {
    return !this.collectingDataFor.has(record);
  }
  //endregion
  //region Render
  beforeRenderRow(eventData) {
    const me = this, histogramData = me.getRecordHistogramData(eventData.record);
    if (!Objects.isPromise(histogramData)) {
      const data = histogramData ? me.extractHistogramDataArray(histogramData, eventData.record) : [];
      if (!data) {
        return;
      }
      if (me.ticksWidth) {
        for (let i = 0, { length } = data; i < length; i++) {
          data[i].width = me.ticksWidth[i];
        }
      }
      const histogramConfig = Objects.merge(
        // reset topValue by default to enable its auto-detection
        { topValue: null },
        me.initialConfig.histogramWidget,
        {
          data,
          series: { ...me.series }
        }
      );
      eventData = {
        ...eventData,
        histogramConfig,
        histogramData,
        histogramWidget: me.histogramWidget
      };
      me.trigger("beforeRenderHistogramRow", eventData);
      delete eventData.eventName;
      delete eventData.source;
      delete eventData.type;
      delete eventData.oldId;
      delete eventData.row;
      delete eventData.recordIndex;
      me._recordRenderData = me.processRecordRenderData(eventData);
    }
    super.beforeRenderRow(...arguments);
  }
  applyHistogramWidgetConfig(histogramWidget = this.histogramWidget, histogramConfig) {
    Object.assign(histogramWidget, histogramWidgetCleanState, histogramConfig);
  }
  /**
   * Renders a histogram for a row.
   * The method applies passed data to the underlying {@link #property-histogramWidget} component.
   * Then the component renders charts and the method injects them into the corresponding column cell.
   * @param {HistogramRenderData} renderData Render data
   * @internal
   */
  renderRecordHistogram(renderData) {
    const me = this, { histogramData, cellElement } = renderData;
    if (!histogramData) {
      cellElement.innerHTML = "";
      return;
    }
    me.trigger("beforeRenderRecordHistogram", renderData);
    delete renderData.eventName;
    delete renderData.type;
    delete renderData.source;
    const histogramWidget = renderData.histogramWidget || me.histogramWidget;
    me.applyHistogramWidgetConfig(histogramWidget, renderData.histogramConfig);
    histogramWidget.refresh({
      // tell histogram we want it to pass renderData as an extra argument in nested calls of getBarText and
      // other configured hooks
      args: [renderData]
    });
    const histogramCloneElement = histogramWidget.element.cloneNode(true);
    histogramCloneElement.removeAttribute("id");
    histogramCloneElement.classList.remove("b-hide-offscreen");
    cellElement.innerHTML = "";
    cellElement.appendChild(histogramCloneElement);
  }
  /**
   * TimeAxis column renderer used by this view to render row histograms.
   * It first calls {@link #function-getRecordHistogramData} method to retrieve
   * the histogram data for the renderer record.
   * If the record data is ready the method renders the record histogram.
   * And in case the method returns a `Promise` the renderer just
   * schedules the record refresh for later and exits.
   *
   * @param {HistogramRenderData} renderData Object containing renderer parameters.
   * @internal
   */
  histogramRenderer(renderData) {
    const me = this, histogramData = renderData.histogramData || me.getRecordHistogramData(renderData.record);
    if (!Objects.isPromise(histogramData)) {
      Object.assign(renderData, me._recordRenderData);
      return me.renderRecordHistogram(...arguments);
    }
    return "";
  }
  /**
   * Group feature hook triggered by the feature to render group headers
   * @param {*} renderData
   * @internal
   */
  buildGroupHeader(renderData) {
    if (renderData.column === this.timeAxisColumn) {
      return this.histogramRenderer(renderData);
    }
    return this.features.group.buildGroupHeader(renderData);
  }
  //endregion
  get widgetClass() {
  }
};
//region Config
__publicField(TimelineHistogramBase, "$name", "TimelineHistogramBase");
__publicField(TimelineHistogramBase, "type", "timelinehistogrambase");
__publicField(TimelineHistogramBase, "configurable", {
  timeCellCls: "b-sch-timeaxis-cell b-timelinehistogram-cell",
  mode: "horizontal",
  rowHeight: 50,
  /**
   * Set to `true` if you want to display a tooltip when hovering an allocation bar. You can also pass a
   * {@link Core/widget/Tooltip#configs} config object.
   * Please use {@link #config-barTooltipTemplate} function to customize the tooltip contents.
   * @prp {Boolean|TooltipConfig}
   */
  showBarTip: false,
  barTooltip: null,
  barTooltipClass: Tooltip,
  /**
   * Object enumerating data series for the histogram.
   * The object keys are treated as the series identifiers and values are objects that
   * must contain two properties:
   *  - `type` A String, either `'bar'` or `'outline'`
   *  - `field` A String, the name of the property to use from the data objects in the {@link #config-data} option.
   *
   * ```javascript
   * histogram = new TimelineHistogram({
   *     ...
   *     series : {
   *         s1 : {
   *             type  : 'bar',
   *             field : 's1'
   *         },
   *         s2 : {
   *             type  : 'outline',
   *             field : 's2'
   *         }
   *     },
   *     store : new Store({
   *         data : [
   *             {
   *                 id            : 'r1',
   *                 name          : 'Record 1',
   *                 histogramData : [
   *                     { s1 : 200, s2 : 100 },
   *                     { s1 : 150, s2 : 50 },
   *                     { s1 : 175, s2 : 50 },
   *                     { s1 : 175, s2 : 75 }
   *                 ]
   *             },
   *             {
   *                 id            : 'r2',
   *                 name          : 'Record 2',
   *                 histogramData : [
   *                     { s1 : 100, s2 : 100 },
   *                     { s1 : 150, s2 : 125 },
   *                     { s1 : 175, s2 : 150 },
   *                     { s1 : 175, s2 : 75 }
   *                 ]
   *             }
   *         ]
   *     })
   * });
   * ```
   *
   * @config {Object<String, HistogramSeries>}
   */
  series: null,
  /**
   * Record field from which the histogram data will be collected.
   *
   * ```javascript
   * histogram = new TimelineHistogram({
   *     ...
   *     series : {
   *         s1 : {
   *             type : 'bar'
   *         }
   *     },
   *     dataModelField : 'foo',
   *     store : new Store({
   *         data : [
   *             {
   *                 id   : 'r1',
   *                 name : 'Record 1',
   *                 foo  : [
   *                     { s1 : 200 },
   *                     { s1 : 150 },
   *                     { s1 : 175 },
   *                     { s1 : 175 }
   *                 ]
   *             },
   *             {
   *                 id   : 'r2',
   *                 name : 'Record 2',
   *                 foo  : [
   *                     { s1 : 100 },
   *                     { s1 : 150 },
   *                     { s1 : 175 },
   *                     { s1 : 175 }
   *                 ]
   *             }
   *         ]
   *     })
   * });
   * ```
   *
   * Alternatively {@link #config-getRecordData} function can be used to build a
   * record's histogram data dynamically.
   * @config {String}
   * @default
   */
  dataModelField: "histogramData",
  /**
   * A function, or name of a function which builds histogram data for the provided record.
   *
   * See also {@link #config-dataModelField} allowing to load histogram data from a record field.
   *
   * @config {Function|String} getRecordData
   * @param {Core.data.Model} getRecordData.record Record to get histogram data for.
   * @param {Object} [aggregationContext] Context object passed in case the data is being retrieved
   * as a part of some parent record data collecting.
   * @returns {Object} Histogram data.
   */
  getRecordData: null,
  /**
   * When set to `true` (default) the component reacts on time axis changes
   * (zooming or changing the displayed time span), clears the histogram data cache of the records
   * and then refreshes the view.
   * @config {Boolean}
   * @default
   */
  hardRefreshOnTimeAxisReconfigure: true,
  /**
   * A Function which returns a CSS class name to add to a rectangle element.
   * The following parameters are passed:
   * @param {HistogramSeries} series The series being rendered
   * @param {DomConfig} domConfig The rectangle configuration object
   * @param {Object} datum The datum being rendered
   * @param {Number} index The index of the datum being rendered
   * @param {HistogramRenderData} renderData Current render data giving access to the record, row and
   * cell being rendered.
   * @returns {String} CSS classes of the rectangle element
   * @config {Function}
   */
  getBarClass: null,
  /**
   * A Function which returns a CSS class name to add to a path element
   * built for an `outline` type series.
   * The following parameters are passed:
   * @param {HistogramSeries} series The series being rendered
   * @param {Object[]} data The series data
   * @param {HistogramRenderData} renderData Current render data giving access to the record, row and
   * cell being rendered.
   * @returns {String} CSS class name of the path element
   * @config {Function}
   */
  getOutlineClass(series) {
    return "";
  },
  /**
   * A Function which if provided should return a DOM configuration object for
   * a `path` element built for an `outline` type series.
   * The function accepts a default prepared DOM configuration
   * in an argument which then can be processed and returned.
   *
   * The following parameters are passed to the function:
   * @param {HistogramSeries} series The series being rendered
   * @param {DomConfig} domConfig The `path` element DOM configuration object
   * @param {Object[]} data The series data
   * @returns {Object} Resulting DOM configuration object. If no value returned the path is
   * not displayed.
   * @config {Function}
   */
  getOutlineDOMConfig(series, domConfig, data) {
    return domConfig;
  },
  readOnly: true,
  /**
   * A Function which returns the tooltip text to display when hovering a bar.
   * The following parameters are passed:
   * @param {Object} context The tooltip context info
   * @param {Object} context.datum The histogram bar being hovered info
   * @param {Core.widget.Tooltip} context.tip The tooltip instance
   * @param {HTMLElement} context.element The Element for which the Tooltip is monitoring mouse movement
   * @param {HTMLElement} context.activeTarget The target element that triggered the show
   * @param {Event} context.event The raw DOM event
   * @param {Core.data.Model} context.record The record which value
   * the hovered bar displays.
   * @returns {String} Tooltip HTML content
   * @config {Function}
   */
  barTooltipTemplate: null,
  /**
   * A Function which returns the text to render inside a bar.
   *
   * ```javascript
   * new TimelineHistogram({
   *     series : {
   *         foo : {
   *             type  : 'bar',
   *             field : 'foo'
   *         }
   *     },
   *     getBarText(datum) {
   *         // display the value in the bar
   *         return datum.foo;
   *     },
   *     ...
   * })
   * ```
   *
   * **Please note** that the function will be injected into the underlying
   * {@link Core/widget/graph/Histogram} component that is used under the hood
   * to render actual charts.
   * So `this` will refer to the {@link Core/widget/graph/Histogram} instance, not
   * this class instance.
   * To access the view please use `this.owner` in the function:
   *
   * ```javascript
   * new TimelineHistogram({
   *     getBarText(datum) {
   *         // "this" in the method refers core Histogram instance
   *         // get the view instance
   *         const timelineHistogram = this.owner;
   *
   *         .....
   *     },
   *     ...
   * })
   * ```
   * The following parameters are passed:
   * @param {Object} datum The datum being rendered
   * @param {Number} index The index of the datum being rendered
   * @param {HistogramSeries} series The series (provided if histogram widget
   * {@link Core/widget/graph/Histogram#config-singleTextForAllBars} is `false`)
   * @param {HistogramRenderData} renderData Current render data giving access to the record, row and
   * cell being rendered.
   * @returns {String} Text to render inside the bar
   * @config {Function}
   */
  getBarText: null,
  /**
   * A Function which if provided should return a DOM configuration object for a bar
   * (a `RECT` element representing a single "bar"-type value).
   * The function is passed a default prepared DOM configuration
   * in an argument which then can be processed and returned.
   *
   * ```javascript
   * new TimelineHistogram({
   *     series : {
   *         foo : {
   *             type  : 'bar',
   *             field : 'foo'
   *         }
   *     },
   *     // Let's add left & right margins to bars
   *     getBarDOMConfig(series, domConfig) {
   *         // margin size is 10% of the bar width
   *         const xMargin = 0.1 * domConfig.width;
   *
   *         // adjust the bar x-coordinate
   *         domConfig.x += xMargin;
   *         // reduce the bar width respectively
   *         domConfig.width -= 2 * xMargin;
   *
   *         // return the edited domConfig
   *         return domConfig;
   *     },
   *     ...
   * })
   * ```
   *
   * {@note}Please note that it's important to return a DOM configuration object. If the function doesn't do
   * that the corresponding bar won't be displayed.{/@note}
   *
   * {@note}The function will be injected into the underlying {@link Core/widget/graph/Histogram} component
   * that is used under the hood to render charts. So `this` will refer to the
   * {@link Core/widget/graph/Histogram} instance, not this class instance.
   * To access the view please use `this.owner` in the function:
   *
   * ```javascript
   * new TimelineHistogram({
   *     getBarText(datum) {
   *         // "this" in the method refers core Histogram instance
   *         // get the view instance
   *         const timelineHistogram = this.owner;
   *
   *         .....
   *     },
   *     ...
   * })
   * ```
   * {/@note}
   *
   * The following parameters are passed:
   * @param {HistogramSeries} series The series being rendered
   * @param {DomConfig} domConfig The rectangle DOM configuration object
   * @param {Object} datum The datum being rendered
   * @param {Number} index The index of the datum being rendered
   * @param {HistogramRenderData} renderData Current render data giving access to the record, row and
   * cell being rendered.
   * @returns {Object} Resulting DOM configuration object. If no value returned the bar is not displayed.
   * @config {Function}
   */
  getBarDOMConfig: null,
  /**
   * A Function which returns a DOM configuration object for text elements.
   *
   * ```javascript
   * new TimelineHistogram({
   *     series : {
   *         foo : {
   *             type  : 'bar',
   *             field : 'foo'
   *         }
   *     },
   *     // display "foo" bar value as text
   *     getBarText(datum) {
   *         return datum.foo;
   *     },
   *
   *     // Place text at the top of the "foo" bar
   *     getBarTextDOMConfig(domConfig, datum, index) {
   *         // to do that we calculate y-position in percents
   *         domConfig.y = `${100 * (1 - datum.foo / this.topValue)}%`;
   *
   *         return domConfig;
   *     },
   *     ...
   * })
   * ```
   *
   * {@note}Please note that it's important to return a DOM configuration object. If the function doesn't do
   * that the corresponding text element won't be displayed.{/@note}
   *
   * {@note}The function will be injected into the underlying
   * {@link Core/widget/graph/Histogram} component that is used under the hood
   * to render actual charts.
   * So `this` will refer to the {@link Core/widget/graph/Histogram} instance, not
   * this class instance.
   * To access the view please use `this.owner` in the function:
   *
   * ```javascript
   * new TimelineHistogram({
   *     getBarTextDOMConfig(domConfig) {
   *         // "this" in the method refers core Histogram instance
   *         // get the view instance
   *         const timelineHistogram = this.owner;
   *
   *         .....
   *
   *         return domConfig;
   *     },
   *     ...
   * })
   * ```
   * {/@note}
   *
   * The following parameters are passed:
   * @param {DomConfig} domConfig The `TEXT` element DOM configuration object
   * @param {Object} datum The datum being rendered
   * @param {Number} index The index of the datum being rendered
   * @param {HistogramSeries} series The series (provided if histogram widget
   * {@link Core/widget/graph/Histogram#config-singleTextForAllBars} is `false`)
   * @param {HistogramRenderData} renderData Current render data giving access to the record, row and
   * cell being rendered.
   * @returns {Object} The `TEXT` element DOM configuration object. If no value returned the text element is not
   * displayed.
   * @config {Function}
   */
  getBarTextDOMConfig: void 0,
  /**
   * The class used for building the {@link #property-histogramWidget histogram widget}
   * @config {Core.widget.graph.Histogram}
   * @default
   */
  histogramWidgetClass: Histogram,
  /**
   * The underlying {@link Core/widget/graph/Histogram} component that is used under the hood
   * to render actual charts.
   * @member {Core.widget.graph.Histogram} histogramWidget
   */
  /**
   * An instance or a configuration object of the underlying {@link Core/widget/graph/Histogram}
   * component that is used under the hood to render actual charts.
   * In case a configuration object is provided the built class is defined with
   * {@link #config-histogramWidgetClass} config.
   * @config {Core.widget.graph.Histogram|HistogramConfig}
   */
  histogramWidget: {
    cls: "b-hide-offscreen b-timelinehistogram-histogram",
    omitZeroHeightBars: true,
    data: []
  },
  fixedRowHeight: true,
  timeAxisColumnConfig: {
    type: "timeAxis",
    renderer: "up.histogramRenderer"
  }
});
TimelineHistogramBase.initClass();
TimelineHistogramBase._$name = "TimelineHistogramBase";

// ../Scheduler/lib/Scheduler/view/mixin/TimelineHistogramGrouping.js
var TimelineHistogramGrouping_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || TimelineHistogramBase) {
    afterConfigure() {
      const me = this;
      me.internalAggregateDataEntry = me.internalAggregateDataEntry.bind(this);
      me.internalInitAggregatedDataEntry = me.internalInitAggregatedDataEntry.bind(this);
      super.afterConfigure();
      if (me.features.treeGroup) {
        me.features.treeGroup.ion({
          // reset groups cache on store grouping change
          beforeDataLoad: me.onTreeGroupBeforeDataLoad,
          thisObj: me
        });
      }
    }
    updateAggregateFunctions(value2) {
      for (const [id2, fn2] of Object.entries(value2)) {
        fn2.id = id2;
        if (fn2.aliases) {
          for (const alias of fn2.aliases) {
            value2[alias] = fn2;
          }
        }
      }
    }
    updateStore(store) {
      super.updateStore(...arguments);
      this.detachListeners("store");
      if (store) {
        store.ion({
          name: "store",
          // reset groups cache on store grouping change
          // Recalculation of group membership is done through sort
          group: this.onStoreGroup,
          sort: this.onStoreSort,
          thisObj: this
        });
      }
    }
    changeAggregateDataEntry(fn2) {
      return this.bindCallback(fn2);
    }
    changeGetDataEntryForAggregating(fn2) {
      return this.bindCallback(fn2);
    }
    changeInitAggregatedDataEntry(fn2) {
      return this.bindCallback(fn2);
    }
    //endregion
    //region Event listeners
    onHistogramDataCacheSet({ record, data }) {
      super.onHistogramDataCacheSet(...arguments);
      if (this.aggregateHistogramDataForGroups) {
        this.scheduleRecordParentsRefresh(record);
      }
    }
    onTreeGroupBeforeDataLoad() {
      if (this.aggregateHistogramDataForGroups) {
        this.resetGeneratedRecordsHistogramDataCache();
      }
    }
    onStoreGroup() {
      if (this.aggregateHistogramDataForGroups) {
        this.resetGeneratedRecordsHistogramDataCache();
      }
    }
    onStoreSort({ source }) {
      if (this.aggregateHistogramDataForGroups && source.isGrouped) {
        this.resetGeneratedRecordsHistogramDataCache();
      }
    }
    //endregion
    // Override getRecordHistogramData to support data aggregating for parents
    getRecordHistogramData(record, aggregationContext) {
      const me = this;
      let result;
      if (me.aggregateHistogramDataForGroups && me.isGroupRecord(record)) {
        result = me.collectingDataFor.get(record) || me.getHistogramDataCache(record);
        if (!result && !me.hasHistogramDataCache(record)) {
          result = me.getGroupRecordHistogramData(record, aggregationContext);
          result = me.finalizeDataRetrieving(record, result);
        }
      } else {
        result = super.getRecordHistogramData(...arguments);
      }
      return result;
    }
    //region ArrayHelper.aggregate default callbacks
    internalAggregateDataEntry(acc, ...args) {
      const { aggregateFunctions } = this;
      for (const { id: id2, aggregate = "sum" } of Object.values(this.series)) {
        let fn2;
        if (aggregate !== false && (fn2 = aggregateFunctions[aggregate].entry)) {
          acc = fn2(id2, acc, ...args);
        }
      }
      return this.aggregateDataEntry ? this.aggregateDataEntry(acc, ...args) : acc;
    }
    internalInitAggregatedDataEntry() {
      const entry = this.initAggregatedDataEntry ? this.initAggregatedDataEntry(...arguments) : {}, { aggregateFunctions } = this;
      for (const { id: id2, aggregate = "sum" } of Object.values(this.series)) {
        const fn2 = aggregateFunctions[aggregate].init;
        if (fn2 && aggregate !== false) {
          fn2(id2, entry, ...arguments);
        }
      }
      return entry;
    }
    //endregion
    //region Public methods
    /**
     * Resets generated records (parents and links) data cache
     */
    resetGeneratedRecordsHistogramDataCache() {
      const { store } = this;
      for (const record of this.getHistogramDataCache().keys()) {
        if (record.isGroupHeader || record.generatedParent || record.isLinked && !store.includes(record)) {
          this.clearHistogramDataCache(record);
        }
      }
    }
    setHistogramDataCache(record, data) {
      super.setHistogramDataCache(record, data);
      if (record.isLinked) {
        super.setHistogramDataCache(record.$original, data);
      } else if (record.$links) {
        const { store } = this;
        for (const link of record.$links) {
          if (store.includes(link)) {
            super.setHistogramDataCache(link, data);
          }
        }
      }
    }
    // Override method to support links built by TreeGroup feature
    // so for the links the method will retrieve original records cache
    getHistogramDataCache(record) {
      let result = super.getHistogramDataCache(record);
      if (!result && record.isLinked) {
        result = super.getHistogramDataCache(record.$original);
      }
      return result;
    }
    /**
     * Aggregates the provided group record children histogram data.
     * If some of the provided records data is not ready yet the method returns a `Promise`
     * that's resolved once the data is ready and aggregated.
     *
     * ```javascript
     * // get parent record aggregated histogram data
     * const aggregatedData = await histogram.getGroupRecordHistogramData(record);
     * ```
     *
     * @param {Core.data.Model} record Group record.
     * @param {Object} [aggregationContext] Optional aggregation context object.
     * When provided will be used as a shared object passed through while collecting the data.
     * So can be used for some custom application purposes.
     * @returns {Object[]|Promise} Either the provided group record histogram data or a `Promise` that
     * returns the data when resolved.
     * @category Parent histogram data collecting
     */
    getGroupRecordHistogramData(record, aggregationContext = {}) {
      aggregationContext.parentRecord = record;
      const result = this.aggregateRecordsHistogramData(this.getGroupChildren(record), aggregationContext);
      return Objects.isPromise(result) ? result.then((res) => res) : result;
    }
    /**
     * Aggregates multiple records histogram data.
     * If some of the provided records data is not ready yet the method returns a `Promise`
     * that's resolved once the data is ready and aggregated.
     *
     * @param {Core.data.Model[]} records Records to aggregate data of.
     * @param {Object} [aggregationContext] Optional aggregation context object.
     * Can be used by to share some data between the aggregation steps.
     * @returns {Object[]|Promise} Either the provided group record histogram data or a `Promise` that
     * returns the data when resolved.
     * @category Parent histogram data collecting
     */
    aggregateRecordsHistogramData(records, aggregationContext = {}) {
      const me = this, recordsData = [], { parentRecord } = aggregationContext;
      let hasPromise = false;
      for (const child of records) {
        const childData = me.getRecordHistogramData(child, aggregationContext);
        hasPromise = hasPromise || Objects.isPromise(childData);
        childData && recordsData.push(childData);
      }
      if (hasPromise) {
        return Promise.all(recordsData).then((values) => {
          aggregationContext.parentRecord = parentRecord;
          values = values.filter((x) => x);
          return records.length > 0 && !me.isDestroyed ? me.aggregateHistogramData(values, records, aggregationContext) : [];
        });
      }
      return me.aggregateHistogramData(recordsData, records, aggregationContext);
    }
    /**
     * Indicates if the passed record represents a group header built by {@link Grid/feature/Group} feature
     * or a group built by {@link Grid/feature/TreeGroup} feature.
     *
     * @param {Core.data.Model} record The view record
     * @returns {Boolean} `true` if the record represents a group.
     * @internal
     */
    isGroupRecord(record) {
      return record.isGroupHeader || this.isTreeGrouped && record.generatedParent;
    }
    /**
     * For a record representing a group built by {@link Grid/feature/Group} or {@link Grid/feature/TreeGroup}
     * feature returns the group members.
     *
     * @param {Core.data.Model} record A group record
     * @returns {Core.data.Model[]} Records belonging to the group
     * @internal
     */
    getGroupChildren(record) {
      return record.groupChildren || record.children;
    }
    /**
     * For a record belonging to a group built by {@link Grid/feature/Group} or {@link Grid/feature/TreeGroup}
     * feature returns the group header or parent respectively.
     *
     * @param {Core.data.Model} record A member record
     * @returns {Core.data.Model} The record group header or parent record
     * @internal
     */
    getRecordParent(record) {
      const { groupParent } = record.instanceMeta(this.store);
      return groupParent || this.isTreeGrouped && record.parent;
    }
    /**
     * Schedules refresh of the provided record's parents.
     * The method iterates up from the provided record parent to the root node
     * and schedules the iterated node rows refresh.
     * @param {Core.data.Model} record Record to refresh parent rows of.
     * @param {Boolean} [clearCache=true] `true` to reset the scheduled records histogram data cache.
     * @internal
     */
    scheduleRecordParentsRefresh(record, clearCache = true) {
      const me = this;
      let groupParent;
      while (groupParent = me.getRecordParent(record)) {
        clearCache && me.clearHistogramDataCache(groupParent);
        me.scheduleRecordRefresh(groupParent);
        record = groupParent;
      }
    }
    //endregion
    /**
     * Aggregates collected child records data to its parent.
     * The method is synchronous and is called when all the child records data is ready.
     * Override the method if you need to preprocess or postprocess parent records aggregated data:
     *
     * ```javascript
     * class MyHistogramView extends TimelineHistogram({
     *
     *     aggregateHistogramData(recordsData, records, aggregationContext) {
     *         const result = super.aggregateHistogramData(recordsData, records, aggregationContext);
     *
     *         // postprocess averageSalary series values collected for a parent record
     *         result.forEach(entry => {
     *             entry.averageSalary = entry.averageSalary / records.length;
     *         });
     *
     *         return result;
     *     }
     *
     * });
     * ```
     *
     * @param {Object[]} recordsData Child records histogram data.
     * @param {Core.data.Model[]} records Child records.
     * @param {Object} aggregationContext An object containing current shared info on the current aggregation process
     */
    aggregateHistogramData(recordsData, records, aggregationContext = {}) {
      const me = this, { aggregateFunctions } = me;
      aggregationContext.recordsData = recordsData;
      aggregationContext.records = records;
      const arrays = recordsData.map((histogramData, index) => {
        return me.extractHistogramDataArray(
          histogramData,
          records[index]
        );
      });
      const result = ArrayHelper.aggregate(
        arrays,
        me.getDataEntryForAggregating || ((entry) => entry),
        me.internalAggregateDataEntry,
        me.internalInitAggregatedDataEntry,
        aggregationContext
      );
      for (const { id: id2, aggregate = "sum" } of Object.values(me.series)) {
        const fn2 = aggregateFunctions[aggregate].finalize;
        if (fn2 && aggregate !== false) {
          fn2(id2, result, ...arguments);
        }
      }
      return result;
    }
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "TimelineHistogramGrouping"), //region Configs
  __publicField(_a5, "configurable", {
    /**
     * When `true` the component will automatically calculate data for group records
     * based on the groups members data by calling {@link #function-getGroupRecordHistogramData} method.
     * @config {Boolean}
     * @category Parent histogram data collecting
     * @default
     */
    aggregateHistogramDataForGroups: true,
    /**
     * A function used for aggregating child records histogram data entries to their parent entry.
     *
     * It's called for each child entry and is meant to apply the child entry values to the
     * target parent entry (provided in `aggregated` parameter).
     * The function must return the resulting aggregated entry that will be passed as `aggregated`
     * parameter to the next __aggregating__ step.
     *
     * Should be provided as a function, or name of a function in the ownership hierarchy which may be called.
     *
     * @config {Function|String} aggregateDataEntry
     * @param {Object} aggregateDataEntry.aggregated Target parent data entry to aggregate the entry into.
     * @param {Object} aggregateDataEntry.entry Current entry to aggregate into `aggregated`.
     * @param {Number} aggregateDataEntry.arrayIndex Index of current array (index of the record among other records being aggregated).
     * @param {Object[]} aggregateDataEntry.entryIndex Index of `entry` in the current array.
     * @returns {Object} Return value becomes the value of the `aggregated` parameter on the next invocation of this function.
     * @category Parent histogram data collecting
     * @default
     */
    aggregateDataEntry: null,
    /**
     * Function that extracts a record histogram data entry for aggregating.
     * By default it returns the entry as is. Override the function if you need a more complex way
     * to retrieve the value for aggregating.
     *
     * Should be provided as a function, or name of a function in the ownership hierarchy which may be called.
     *
     * @config {Function|String} getDataEntryForAggregating
     * @param {Object} getDataEntryForAggregating.entry Current data entry.
     * @returns {Object} Entry to aggregate
     * @category Parent histogram data collecting
     * @default
     */
    getDataEntryForAggregating: null,
    /**
     * A function that initializes a target group record entry.
     *
     * Should be provided as a function, or name of a function in the ownership hierarchy which may be called.
     *
     * @config {Function|String} initAggregatedDataEntry
     * @returns {Object} Target aggregated entry
     * @category Parent histogram data collecting
     * @default
     */
    initAggregatedDataEntry: null,
    aggregateFunctions: {
      sum: {
        aliases: ["add"],
        entry(seriesId, acc, entry) {
          acc[seriesId] = (acc[seriesId] || 0) + entry[seriesId];
          return acc;
        }
      },
      min: {
        entry(seriesId, acc, entry) {
          const entryValue = entry[seriesId];
          if (entryValue < (acc[seriesId] || Number.MAX_VALUE))
            acc[seriesId] = entryValue;
          return acc;
        }
      },
      max: {
        entry(seriesId, acc, entry) {
          const entryValue = entry[seriesId];
          if (entryValue > (acc[seriesId] || Number.MIN_VALUE))
            acc[seriesId] = entryValue;
          return acc;
        }
      },
      count: {
        init(seriesId, entry, entryIndex, aggregationContext) {
          entry[seriesId] = aggregationContext.arrays.length;
        }
      },
      avg: {
        entry(seriesId, acc, entry) {
          acc[seriesId] = (acc[seriesId] || 0) + entry[seriesId];
          return acc;
        },
        finalize(seriesId, data, recordsData, records, aggregationContext) {
          const cnt = aggregationContext.arrays.length;
          data.forEach((entry) => entry[seriesId] /= cnt);
        }
      }
    }
  }), _a5;
};

// ../Scheduler/lib/Scheduler/view/mixin/TimelineHistogramScaleColumn.js
var TimelineHistogramScaleColumn_default = (Target) => {
  var _a5;
  return _a5 = class extends Target {
    changeScalePoints(value2) {
      value2 == null ? void 0 : value2.sort((a, b) => a.value - b.value);
      return value2;
    }
    updateScalePoints(scalePoints) {
      const me = this, topScalePoint = scalePoints[scalePoints.length - 1];
      if (topScalePoint) {
        me.scaleUnit = topScalePoint.unit;
        if (me.calculateTopValueByScalePoints) {
          me.histogramWidget.topValue = me.getTopValueByScalePoints(scalePoints);
        }
      }
      if (me.scaleColumn) {
        me.scaleColumn.scalePoints = scalePoints;
      }
    }
    //endregion
    //region Columns
    changeColumns(columns, currentStore) {
      const me = this, scaleColumn = me.getConfig("scaleColumn");
      if (columns && scaleColumn) {
        const isArray4 = Array.isArray(columns);
        let cols = columns;
        if (!isArray4) {
          cols = columns.data;
        }
        let scaleColumnIndex = cols == null ? void 0 : cols.length, scaleColumnConfig = scaleColumn;
        cols.some((col, index) => {
          if (col.type === "scale") {
            scaleColumnIndex = index;
            scaleColumnConfig = ObjectHelper.assign(col, scaleColumnConfig);
            return true;
          }
        });
        cols = cols.slice();
        cols[scaleColumnIndex] = {
          type: "scale",
          ...scaleColumnConfig
        };
        if (isArray4) {
          columns = cols;
        } else {
          columns.data = cols;
        }
      }
      return super.changeColumns(columns, currentStore);
    }
    updateColumns(columns, was) {
      super.updateColumns(columns, was);
      if (columns) {
        this._scaleColumn = this.columns.find((c) => c.isScaleColumn);
      }
    }
    onColumnsChanged({ action, changes, record: column, records }) {
      const { scaleColumn, columns } = this;
      if (scaleColumn && (action === "dataset" || action === "batch") && !columns.includes(scaleColumn)) {
        columns.add(scaleColumn, true);
      }
      super.onColumnsChanged(...arguments);
    }
    //endregion
    //region Data processing
    /**
     * A hook to convert scale point values to histogram ones.
     * In case they use different units.
     *
     * Override this method in a sub-class to implement your custom
     * application specific conversion.
     * @param {Number} value Scale point value
     * @param {String} unit Scale point unit
     * @internal
     */
    convertUnitsToHistogramValue(value2, unit) {
      return value2;
    }
    /**
     * A hook to convert histogram values to scale point ones.
     * In case they use different units.
     *
     * Override this method in a sub-class to implement your custom
     * application specific conversion.
     * @param {Number} value Scale point value
     * @param {String} unit Scale point unit
     * @internal
     */
    convertHistogramValueToUnits(value2, unit) {
      return value2;
    }
    extractHistogramDataArray(histogramData, record) {
      return histogramData;
    }
    getTopValueByScalePoints(scalePoints) {
      const me = this, { scaleColumn } = me, lastPoint = scalePoints[scalePoints.length - 1], { value: value2, unit } = lastPoint;
      let rawValue = value2;
      if (scaleColumn) {
        rawValue *= 1 + (scaleColumn.scaleWidget.scaleMaxPadding || 0);
      }
      return me.convertUnitsToHistogramValue(rawValue, unit || me.scaleUnit);
    }
    processRecordRenderData(renderData) {
      var _a6;
      renderData = super.processRecordRenderData(...arguments);
      const { scaleColumn } = this;
      if (scaleColumn) {
        const me = this, { record, histogramData, histogramConfig = {} } = renderData;
        let topValue = (_a6 = me.initialConfig.histogramWidget) == null ? void 0 : _a6.topValue, scalePoints = me.scalePoints || record.get(scaleColumn.field);
        if (!topValue) {
          if (scalePoints && me.calculateTopValueByScalePoints) {
            topValue = me.getTopValueByScalePoints(scalePoints);
          }
          if (!topValue && histogramData) {
            const histogramWidget = renderData.histogramWidget || me.histogramWidget;
            ObjectHelper.assign(histogramWidget, histogramConfig);
            topValue = histogramWidget.getDataTopValue(histogramData);
            scalePoints = [{
              value: me.convertHistogramValueToUnits(topValue, me.scaleUnit),
              text: me.convertHistogramValueToUnits(topValue, me.scaleUnit)
            }];
            topValue += me.scaleColumn.scaleWidget.scaleMaxPadding * topValue;
          }
          renderData.scaleWidgetConfig = { scalePoints };
          renderData.histogramConfig = { ...histogramConfig, topValue };
        }
      }
      return renderData;
    }
    //endregion
    //region Render
    /**
     * Group feature hook triggered by the feature to render group headers
     * @param {Object} renderData
     * @internal
     */
    buildGroupHeader(renderData) {
      if (renderData.column === this.scaleColumn) {
        return this.scaleColumn.renderer(renderData);
      }
      return super.buildGroupHeader(...arguments);
    }
    beforeRenderCell(renderData) {
      if (this.scaleColumn && renderData.column === this.scaleColumn) {
        renderData.histogramData = this.getRecordHistogramData(renderData.record);
        if (!ObjectHelper.isPromise(renderData.histogramData)) {
          Object.assign(renderData, this._recordRenderData);
        }
      }
      return super.beforeRenderCell(...arguments);
    }
    /**
     * Renders record scale column content.
     * @param {Core.data.Model} record Record to render scale for
     * @param {Object} [renderData]
     * @category Scale column
     */
    renderRecordScale(record, renderData) {
      if (this.scaleColumn) {
        const row = this.getRowFor(record), cellElement = row == null ? void 0 : row.getCell(this.scaleColumn.id);
        if (cellElement) {
          row.renderCell(cellElement);
        }
      }
    }
    get widgetClass() {
    }
    //endregion
  }, __publicField(_a5, "$name", "TimelineHistogramScaleColumn"), //region Config
  __publicField(_a5, "configurable", {
    /**
     * The locked grid scale column reference.
     * @member {Scheduler.column.ScaleColumn} scaleColumn
     * @readonly
     * @category Scale column
     */
    /**
     * An object with configuration for the {@link Scheduler/column/ScaleColumn}.
     *
     * Example:
     *
     * ```javascript
     * new TimelineHistogram({
     *     scaleColumn : {
     *         width : 50
     *     },
     *     ...
     * });
     * ```
     *
     * Provide `null` to the config to get rid of the column completely:
     *
     * ```javascript
     * new TimelineHistogram({
     *     // do not add scale column
     *     scaleColumn : null,
     *     ...
     * });
     * ```
     *
     * @config {ScaleColumnConfig} scaleColumn
     * @category Scale column
     */
    scaleColumn: {},
    /**
     * Array of objects representing the {@link #config-scaleColumn scale column} scale points.
     * The config basically is a mapping to the column {@link Scheduler/column/ScaleColumn#config-scalePoints}
     * config.
     *
     * If the config value is not provided the column will try
     * reading it from the displayed record {@link Scheduler/column/ScaleColumn#config-field}
     * (`scalePoints` field is expected by default).
     *
     * ```javascript
     * new TimelineHistogram({
     *     scalePoints : [
     *         { text : '8h', value : 8 },
     *         { text : '16h', value : 16 },
     *         { text : '24h', value : 24 }
     *     ]
     * })
     * ```
     * @config {ScalePoint[]} scalePoints
     */
    scalePoints: null,
    calculateTopValueByScalePoints: true
  }), _a5;
};

// ../Scheduler/lib/Scheduler/view/TimelineHistogram.js
var TimelineHistogram = class extends TimelineHistogramBase.mixin(
  TimelineHistogramGrouping_default,
  TimelineHistogramScaleColumn_default
) {
  /**
   * Retrieves the histogram data for the provided record.
   *
   * The method first checks if there is cached data for the record and returns it if found.
   * Otherwise it starts collecting data by calling {@link #config-getRecordData} (if provided)
   * or by reading it from the {@link #config-dataModelField} record field.
   *
   * If the provided record represents a group and {@link #config-aggregateHistogramDataForGroups} is enabled
   * then the group members data is calculated with a {@link #function-getGroupRecordHistogramData} method call.
   *
   * The method can be asynchronous depending on the provided {@link #config-getRecordData} function.
   * If the function returns a `Promise` then the method will return a wrapping `Promise` in turn that will
   * resolve with the collected histogram data.
   *
   * The method triggers the {@link #event-histogramDataCacheSet} event when a record data is ready.
   *
   * @param {Core.data.Model} record Record to retrieve the histogram data for.
   * @param {Object} [aggregationContext] An optional object passed when the method is called when aggregating
   * a group members histogram data.
   *
   * See {@link #function-getGroupRecordHistogramData} and {@link Core/helper/ArrayHelper#function-aggregate-static}
   * for more details.
   * @returns {Object|Promise} The histogram data for the provided record or a `Promise` that will provide the data
   * when resolved.
   * @function getRecordHistogramData
   */
};
//region Config
__publicField(TimelineHistogram, "$name", "TimelineHistogram");
__publicField(TimelineHistogram, "type", "timelinehistogram");
TimelineHistogram.initClass();
TimelineHistogram._$name = "TimelineHistogram";

// ../Scheduler/lib/Scheduler/widget/ProjectCombo.js
var ProjectCombo = class extends Combo {
  static get configurable() {
    return {
      /**
       * Project to reconfigure when picking an item.
       * @config {Scheduler.model.ProjectModel}
       * @category Common
       */
      project: null,
      /**
       * Field used as projects title.
       * @config {String}
       * @default
       * @category Common
       */
      displayField: "title",
      /**
       * Field used as projects load url.
       * @config {String}
       * @default
       * @category Common
       */
      valueField: "url",
      highlightExternalChange: false,
      editable: false,
      internalListeners: {
        change: "onInternalChange"
      }
    };
  }
  updateProject(project) {
    var _a5;
    if ((_a5 = project.transport.load) == null ? void 0 : _a5.url) {
      this.value = project.transport.load.url;
    }
  }
  onInternalChange({ value: value2, userAction }) {
    if (userAction && this.project) {
      this.project.transport.load.url = value2;
      this.project.load();
    }
  }
};
__publicField(ProjectCombo, "$name", "ProjectCombo");
__publicField(ProjectCombo, "type", "projectcombo");
ProjectCombo.initClass();
ProjectCombo._$name = "ProjectCombo";

// ../Scheduler/lib/Scheduler/widget/ResourceFilter.js
var ResourceFilter = class extends List {
  static get delayable() {
    return {
      applyFilters: "raf"
    };
  }
  static get configurable() {
    return {
      /**
       * The {@link Scheduler.data.EventStore} to filter.
       * Events for resources which are deselected in this List will be filtered out.
       * @config {Scheduler.data.EventStore}
       */
      eventStore: null,
      /**
       * Configure as `false` to only allow selecting one resource at a time
       * @prp {Boolean}
       * @default
       */
      multiSelect: true,
      /**
       * Configure as `false` to not use the Resource`s color field to style the checkbox
       * @config {Boolean}
       * @default
       */
      useResourceColor: true,
      toggleAllIfCtrlPressed: true,
      collapsibleGroups: true,
      itemTpl: (record) => StringHelper.encodeHtml(record.name || ""),
      /**
       * An optional filter function to apply when loading resources from the project's
       * resource store. Defaults to loading all resources.
       *
       * **This is called using this `ResourceFilter` as the `this` object.**
       *
       * @config {Function|String}
       * @param {Scheduler.model.ResourceModel} resource Resorce for filtering
       * @returns {Boolean} Returns `true` to include the passed resource
       * @default
       */
      masterFilter: (resource) => true,
      /**
       * By default, deselecting list items filters only the {@link #config-eventStore} so that
       * events for the deselected resources are hidden from view. The `resourceStore` is __not__
       * filtered.
       *
       * Configure this as `true` to also filter the `resourceStore` so that deselected resources
       * are also hidden from view (They will remain in this `List`)
       * @config {Boolean}
       * @default false
       */
      filterResources: null,
      allowGroupSelect: false
    };
  }
  itemIconTpl(record, i) {
    const { eventColor } = record, { useResourceColor } = this, cls = DomHelper.isNamedColor(eventColor) && useResourceColor ? ` b-sch-color-${eventColor}` : "", style = !cls && eventColor && useResourceColor ? ` style="--checkbox-background-color:${eventColor}"` : "";
    return this.multiSelect && this.callback(this.isSelectable, this, [record]) ? `<div aria-hidden="true" class="b-selected-icon b-icon${cls}"${style}></div>` : "";
  }
  changeStore(store) {
    if (this.eventStore) {
      return super.changeStore(...arguments);
    } else {
      this._storeConfig = store;
    }
  }
  changeEventStore(eventStore) {
    this.getConfig("store");
    return eventStore;
  }
  updateEventStore(eventStore) {
    var _a5;
    const me = this, chainedStoreConfig = me._storeConfig || {}, { resourceStore } = eventStore, storeConfig = {
      ...chainedStoreConfig,
      syncSort: true,
      excludeCollapsedRecords: false
    }, changeListeners = {
      name: "resourceStore",
      change: "onStoreChange",
      thisObj: me
    };
    if (resourceStore.tree) {
      storeConfig.sorters = [];
    }
    (_a5 = me.store) == null ? void 0 : _a5.destroy();
    me.detachListeners("project", "resourceStore");
    const store = me.store = resourceStore.chain(me.masterFilter, null, storeConfig);
    store.un(changeListeners);
    resourceStore.ion(changeListeners);
    if (!resourceStore.count) {
      resourceStore.project.ion({
        name: "project",
        refresh: "initFilter",
        thisObj: me
      });
    } else {
      me.initFilter();
    }
  }
  changeMasterFilter(masterFilter) {
    const me = this;
    if (!me.filterResources) {
      return function(r) {
        return me.callback(masterFilter, me, [r]);
      };
    }
  }
  initFilter() {
    var _a5, _b2;
    const me = this;
    if (me.eventStore.resourceStore.count) {
      const { selected } = me;
      if (!selected.count) {
        if (((_a5 = me.initialConfig.selected) == null ? void 0 : _a5.length) === 0) {
          me.onInternalSelectionChange({ source: selected, added: [], removed: [] });
        } else {
          if ((_b2 = me.initialConfig.selected) == null ? void 0 : _b2.length) {
            me.selected = me.initialConfig.selected;
          } else {
            selected.add(me.store.allRecords.filter((r) => !r.isGroupHeader));
          }
        }
      }
      me.detachListeners("project");
    }
  }
  onStoreRefresh({ source: store, action }) {
    if (action === "filter" && this.eventStoreFilter) {
      const { eventStoreFilter } = this, { disabled } = eventStoreFilter, newDisabled = !store.isFiltered && this.allSelected;
      if (newDisabled !== disabled) {
        eventStoreFilter.disabled = newDisabled;
        this.applyFilters();
      }
    }
    super.onStoreRefresh(...arguments);
  }
  onInternalSelectionChange({ source: selected, added = [], removed }) {
    const me = this, disabled = !me.store.isFiltered && me.allSelected;
    super.onInternalSelectionChange(...arguments);
    let filtersAdded = false;
    if (!me.eventStoreFilter) {
      me.eventStoreFilter = me.eventStore.addFilter({
        id: `${me.id}-filter-instance`,
        filterBy: (e) => !e.resource || me.selected.includes(e.resources),
        disabled
      }, (added == null ? void 0 : added.length) === me.store.count);
      filtersAdded = true;
    }
    if (me.filterResources && !me.resourceStoreFilter) {
      me.resourceStoreFilter = me.eventStore.resourceStore.addFilter({
        id: `${me.id}-filter-instance`,
        filterBy: (r) => me.selected.includes(r),
        disabled
      }, (added == null ? void 0 : added.length) === me.store.count);
      filtersAdded = true;
    }
    if (filtersAdded) {
      return;
    }
    me.eventStoreFilter.disabled = disabled;
    me.resourceStoreFilter && (me.resourceStoreFilter.disabled = disabled);
    me.applyFilters();
    const value2 = selected.values, oldValue = value2.concat(removed);
    ArrayHelper.remove(oldValue, ...added);
    me.triggerFieldChange({
      value: value2,
      oldValue
    });
  }
  /**
   * An array encapsulating the currently selected resources.
   * @member {Scheduler.model.ResourceModel[]}
   * @readonly
   */
  get value() {
    return this.selected.values;
  }
  applyFilters() {
    this.eventStore.filter();
    this.filterResources && this.eventStore.resourceStore.filter();
  }
  updateUseResourceColor(value2) {
    this.element.classList.toggle("b-plain-color", !value2);
  }
  doDestroy() {
    var _a5;
    (_a5 = this.store) == null ? void 0 : _a5.destroy();
    super.doDestroy();
  }
};
__publicField(ResourceFilter, "$name", "ResourceFilter");
// Factoryable type name
__publicField(ResourceFilter, "type", "resourcefilter");
ResourceFilter.initClass();
ResourceFilter._$name = "ResourceFilter";

// ../Scheduler/lib/Scheduler/widget/SchedulerDatePicker.js
var _SchedulerDatePicker = class _SchedulerDatePicker extends DatePicker {
  changeEventFilter(eventFilter) {
    if (typeof eventFilter === "string") {
      const { handler, thisObj } = this.resolveCallback(eventFilter);
      eventFilter = handler.bind(thisObj);
    }
    return eventFilter;
  }
  doRefresh() {
    if (this.isVisible || !this.showEvents) {
      this.refreshEventsMap();
      return super.doRefresh(...arguments);
    } else {
      this.whenVisible("doRefresh");
    }
  }
  updateShowEvents(showEvents, oldShowEvents) {
    const me = this, { classList } = me.contentElement;
    let { eventStore } = me;
    me.requestAnimationFrame(() => {
      var _a5;
      me.element.classList.toggle("b-datepicker-with-events", Boolean(showEvents));
      (_a5 = me.owner) == null ? void 0 : _a5.element.classList.toggle("b-datepicker-with-events", Boolean(showEvents));
      showEvents && classList.add(`b-show-events-${showEvents}`);
      classList.remove(`b-show-events-${oldShowEvents}`);
    });
    if (showEvents) {
      if (!eventStore) {
        const eventStoreOwner = me.up((w) => w.eventStore);
        if (eventStoreOwner) {
          me._eventStore = eventStore = eventStoreOwner.eventStore;
        } else {
          throw new Error("DatePicker configured with events but no eventStore");
        }
      }
    } else {
      me.cellMap = null;
    }
    if (!me.isConfiguring) {
      me.updateEventStore(eventStore);
      me.doRefresh();
    }
  }
  refreshEventsMap() {
    const me = this;
    if (me.showEvents) {
      me.cellMap = me.eventStore.getEventCounts({
        startDate: me.startDate,
        endDate: me.endDate,
        dateMap: me.cellMap,
        filter: me.eventFilter
      });
    }
  }
  updateEventStore(eventStore) {
    var _a5;
    if (eventStore.findListener("change", "refresh", this) === -1) {
      (_a5 = eventStore == null ? void 0 : eventStore[this.showEvents ? "on" : "un"]) == null ? void 0 : _a5.call(eventStore, {
        change: "refresh",
        thisObj: this
      });
    }
  }
  cellRenderer({ cell, date: date2 }) {
    var _a5, _b2;
    const { showEvents } = this, cellData = (_b2 = (_a5 = this.cellMap) == null ? void 0 : _a5.get) == null ? void 0 : _b2.call(_a5, DateHelper.makeKey(date2)), count = Array.isArray(cellData) ? cellData.length : cellData, isCount = showEvents === "count";
    delete cell.dataset.btip;
    if (count) {
      if (!isCount && this.eventCountTip) {
        cell.dataset.btip = this.L("L{ResourceInfoColumn.eventCountText}", count);
      }
      DomHelper.createElement({
        dataset: {
          count
        },
        class: {
          [isCount ? "b-cell-events-badge" : "b-icon b-icon-circle"]: showEvents !== "heatmap",
          [_SchedulerDatePicker.getEventCountClass(count)]: 1
        },
        parent: cell,
        [isCount ? "text" : ""]: count
      });
    }
  }
  getCellClassList({ date: date2 }) {
    var _a5, _b2;
    const cellData = (_b2 = (_a5 = this.cellMap) == null ? void 0 : _a5.get) == null ? void 0 : _b2.call(_a5, DateHelper.makeKey(date2)), count = Array.isArray(cellData) ? cellData.length : cellData, result = super.getCellClassList(...arguments);
    result[_SchedulerDatePicker.getEventCountClass(count)] = count > 0;
    return result;
  }
  static getEventCountClass(count) {
    if (count) {
      if (count < 4) {
        return "b-datepicker-1-to-3-events";
      }
      if (count < 7) {
        return "b-datepicker-4-to-6-events";
      }
      return "b-datepicker-7-or-more-events";
    }
    return "";
  }
  static setupClass(meta) {
    meta.replaceType = true;
    super.setupClass(meta);
  }
};
__publicField(_SchedulerDatePicker, "$name", "SchedulerDatePicker");
__publicField(_SchedulerDatePicker, "type", "datepicker");
__publicField(_SchedulerDatePicker, "configurable", {
  /**
   * How to show presence of events in the configured {@link #config-eventStore} in the
   * day cells. Values may be:
   *
   * * `false` - Do not show events in cells.
   * * `true` - Show a themeable bullet to indicate the presence of events for a date.
   * * `'count'` - Show a themeable badge containing the event count for a date.
   * * `'heatmap'` - show warmer background colors the more events are present for a date
   * @config {Boolean|'count'|'heatmap'}
   * @default false
   */
  showEvents: null,
  /**
   * The {@link Scheduler.data.EventStore event store} from which the in-cell event presence
   * indicators are drawn.
   * @config {Scheduler.data.EventStore}
   */
  eventStore: null,
  /**
   * A function, or the name of a function in the ownership hierarchy to filter which events
   * are collected into the day cell data blocks.
   *
   * Return `true` to include the passed event, or a *falsy* value to exclude the event.
   *
   * @config {Function|String}
   * @param {Scheduler.model.EventModel} event Event for filtering
   * @returns {Boolean} Return `true` to include the passed event
   */
  eventFilter: {
    $config: "lazy",
    value: null
  }
});
var SchedulerDatePicker = _SchedulerDatePicker;
SchedulerDatePicker.initClass();
SchedulerDatePicker._$name = "SchedulerDatePicker";

// ../Scheduler/lib/Scheduler/widget/UndoRedo.js
var isProjectConsumer = (w) => w.isProjectConsumer;
var _UndoRedo = class _UndoRedo extends UndoRedoBase {
  construct() {
    super.construct(...arguments);
    if (!this.stm) {
      this.scheduler = this.up(isProjectConsumer);
    }
  }
  changeScheduler(scheduler) {
    return scheduler.isProjectConsumer ? scheduler : _UndoRedo.getById(scheduler);
  }
  updateScheduler(scheduler) {
    const me = this, { crudManager } = scheduler;
    scheduler.ion({
      projectChange: "onProjectChanged",
      thisObj: me
    });
    if (crudManager) {
      me.setupLoadListener(crudManager);
    } else {
      me.onLoad();
    }
    if (!me.project && (scheduler == null ? void 0 : scheduler.project)) {
      me.project = scheduler.project;
    }
    me.stm = scheduler.project.stm;
  }
  setupLoadListener(source) {
    this.detachListeners("load");
    source.ion({
      name: "load",
      load: "onLoad",
      thisObj: this
    });
  }
  async onLoad() {
    await this.scheduler.project.commitAsync();
    if (!this.isDestroyed) {
      this.stm.enable();
      this.fillUndoRedoCombo();
    }
  }
  onProjectChanged({ project }) {
    this.project = project;
  }
  updateProject(project) {
    super.updateProject(...arguments);
    if (project && this.scheduler && !this.scheduler.isConfiguring) {
      this.setupLoadListener(project);
    }
  }
};
__publicField(_UndoRedo, "$name", "UndoRedo");
__publicField(_UndoRedo, "type", "undoredo");
__publicField(_UndoRedo, "configurable", {
  /**
   * The Scheduling Widget (or its `id`) whose transaction to track.
   *
   * This may be a `Scheduler`, a `Gantt` or a `Calendar`.
   *
   * ```javascript
   *     {
   *         type      : 'undoredo',
   *         scheduler : myCalendar
   *     }
   * ```
   * @config {Core.widget.Widget|String}
   */
  scheduler: null,
  /**
   * Get/set ProjectModel instance, containing the data visualized by the SchedulerPro.
   * @member {Scheduler.model.ProjectModel} project
   * @category Data
   */
  /**
   * The Scheduling {@link Scheduler.model.ProjectModel project}'s whose
   * transaction to track.
   *
   * ```javascript
   *     {
   *         type    : 'undoredo',
   *         project : scheduler.project
   *     }
   * ```
   * @config {Scheduler.model.ProjectModel|ProjectModelConfig}
   * @category Data
   */
  project: null
});
var UndoRedo = _UndoRedo;
UndoRedo.initClass();
UndoRedo._$name = "UndoRedo";

// ../Scheduler/lib/Scheduler/widget/ViewPresetCombo.js
var ViewPresetCombo = class extends Combo {
  construct() {
    super.construct(...arguments);
    this.scheduler.ion({
      presetchange: this.onClientPresetChange,
      thisObj: this
    });
  }
  // Returns current client (Scheduler, SchedulerPro or Gantt)
  get scheduler() {
    return this.client || this.up((widget) => widget.isTimelineBase) || Widget.query((widget) => widget.isTimelineBase);
  }
  changeValue(value2, oldValue) {
    this.getConfig("presets");
    return super.changeValue(value2, oldValue);
  }
  // Creates a chained store of the clients presets store filtered and sorted by the presets config
  updatePresets(presets) {
    this.store = this.scheduler.presets.chain((r) => presets.includes(r.id));
    this.store.sort((a, b) => presets.indexOf(a.id) - presets.indexOf(b.id));
  }
  // When client preset is changed from somewhere else, zooming for example.
  onClientPresetChange({ preset }) {
    const me = this;
    if (!me._isSelecting) {
      me.isSettingValue = true;
      if (me.store.includes(preset.id)) {
        me.value = preset;
      } else {
        me.clear();
      }
      me.isSettingValue = false;
    }
  }
  onSelect({ record }) {
    var _a5;
    if (!this.isSettingValue) {
      const { scheduler } = this, { mainUnit, start, defaultSpan } = record, options = {};
      if (mainUnit && defaultSpan && this.useFixedDuration) {
        let beginningOf = mainUnit;
        if (start && typeof start === "string") {
          beginningOf = (_a5 = DateHelper.parseTimeUnit(start)) != null ? _a5 : start;
        }
        options.startDate = DateHelper.startOf(scheduler.viewportCenterDate, beginningOf);
        if (typeof start === "number") {
          options.startDate = DateHelper.add(options.startDate, start, mainUnit);
        }
        options.endDate = DateHelper.add(options.startDate, defaultSpan, mainUnit);
      }
      scheduler.zoomToLevel(record, options);
    }
  }
  // If underlying store localizes, we need to sync the input field value
  updateLocalization() {
    super.updateLocalization();
    this.syncInputFieldValue();
  }
};
__publicField(ViewPresetCombo, "$name", "ViewPresetCombo");
__publicField(ViewPresetCombo, "type", "viewpresetcombo");
__publicField(ViewPresetCombo, "configurable", {
  /**
   * An array containing string {@link Scheduler.preset.ViewPreset} ids available for selection. The specified
   * presets must be {@link Scheduler.view.mixin.TimelineViewPresets#config-presets available} for the
   * {@link #config-client} (Scheduler, SchedulerPro or Gantt) for it to work properly. The selectable presets
   * will be arranged in the order provided here.
   * @config {Array}
   */
  presets: ["hourAndDay", "weekAndDay", "dayAndMonth"],
  /**
   * If not added to a toolbar, provide a Scheduler, SchedulerPro or Gantt component instance to which the
   * ViewPresetCombo should be connected.
   * @config {Scheduler.view.TimelineBase}
   * @default
   */
  client: null,
  /**
   * As default, a preset change by the ViewPresetCombo will result in that the `startDate` will be calculated to
   * the beginning of the {@link Scheduler.preset.ViewPreset#field-mainUnit} of the
   * {@link Scheduler.view.mixin.TimelineDateMapper#property-viewportCenterDate}. If the ViewPreset has a
   * {@link Scheduler.preset.ViewPreset#field-start} configured, this will be added to the calculation. The
   * `endDate` will then be calculated by adding the ViewPreset's
   * {@link Scheduler.preset.ViewPreset#field-defaultSpan} to the `startDate`.
   *
   * If this is set to `false`, the preset change will result in a call to
   * {@link Scheduler.view.mixin.TimelineZoomable#function-zoomToLevel} without any options. The default behaviour
   * of zoom functionality is to keep the timespan about the same width. If you want to change ViewPreset/Zoom
   * without any changes to the visible timespan, you can set the
   * {@link Scheduler.view.mixin.TimelineZoomable#config-zoomKeepsOriginalTimespan} config to `true`.
   * @config {Boolean}
   */
  useFixedDuration: true,
  /**
   * @hideconfigs caseSensitive, chipView, clearTextOnPickerHide, createOnUnmatched, displayField,
   * displayValueRenderer, emptyText, encodeFilterParams, filterOnEnter, filterOperator, filterParamName,
   * filterSelected, hideTrigger, inlinePicker, items, keyStrokeFilterDelay, minChars, multiSelect,
   * multiValueSeparator, primaryFilter, store, triggerAction, validateFilter, value, valueField,
   * containValues, container, inline, adopt, dataset, title, autoSelect, clearable, highlightExternalChange,
   * keyStrokeChangeDelay, maxLength, minLength, placeholder, required, revertOnEscape, triggers, autoComplete,
   * inputType, spellCheck, validateOnInput, tooltip, autoClose, autoExpand, picker, pickerAlignElement
   */
  /**
   * @hideproperties filterOperator, isEmpty, queryLast, records, store, valueCollection, content, contentElement,
   * dataset, html, overflowElement, errorTip, isEmptyInput, isValid, triggers, scrollable, cellInfo, tab
   */
  /**
   * @hidefunctions clear, clearError, getErrors, select, setError, contains, exitFullscreen, requestFullscreen,
   * owns, query, queryAll
   */
  /**
   * @hideevents input
   */
  editable: false,
  valueField: "id",
  displayField: "name",
  placeholder: "Select view"
});
ViewPresetCombo.initClass();
ViewPresetCombo._$name = "ViewPresetCombo";

// ../Engine/lib/Engine/calendar/CalendarCacheIntervalMultiple.js
var CalendarCacheIntervalMultiple = class _CalendarCacheIntervalMultiple {
  constructor(config) {
    this.intervalGroups = [];
    config && Object.assign(this, config);
  }
  combineWith(interval) {
    const copy = this.intervalGroups.slice();
    copy.push([interval.calendar, interval]);
    return new _CalendarCacheIntervalMultiple({ intervalGroups: copy });
  }
  getIsWorkingForEvery() {
    if (this.isWorkingForEvery != null)
      return this.isWorkingForEvery;
    for (let [_calendar, intervals] of this.getGroups()) {
      if (!intervals[0].isWorking)
        return this.isWorkingForEvery = false;
    }
    return this.isWorkingForEvery = true;
  }
  getIsWorkingForSome() {
    if (this.isWorkingForSome != null)
      return this.isWorkingForSome;
    for (let [_calendar, intervals] of this.getGroups()) {
      if (intervals[0].isWorking)
        return this.isWorkingForSome = true;
    }
    return this.isWorkingForSome = false;
  }
  getCalendars() {
    this.getGroups();
    return this.calendars;
  }
  isCalendarWorking(calendar) {
    return this.getCalendarsWorkStatus().get(calendar);
  }
  getCalendarsWorkStatus() {
    if (this.calendarsWorkStatus)
      return this.calendarsWorkStatus;
    const res = /* @__PURE__ */ new Map();
    for (let [calendar, intervals] of this.getGroups()) {
      res.set(calendar, intervals[0].isWorking);
    }
    return this.calendarsWorkStatus = res;
  }
  getCalendarsWorking() {
    if (this.calendarsWorking)
      return this.calendarsWorking;
    const calendars = [];
    for (let [calendar, intervals] of this.getGroups()) {
      if (intervals[0].isWorking)
        calendars.push(calendar);
    }
    return this.calendarsWorking = calendars;
  }
  getCalendarsNonWorking() {
    if (this.calendarsNonWorking)
      return this.calendarsNonWorking;
    const calendars = [];
    for (let [calendar, intervals] of this.getGroups()) {
      if (!intervals[0].isWorking)
        calendars.push(calendar);
    }
    return this.calendarsNonWorking = calendars;
  }
  getGroups() {
    if (this.intervalsByCalendar)
      return this.intervalsByCalendar;
    const calendars = this.calendars = [];
    const intervalsByCalendar = /* @__PURE__ */ new Map();
    this.intervalGroups.forEach(([calendar, interval]) => {
      let data = intervalsByCalendar.get(calendar);
      if (!data) {
        calendars.push(calendar);
        data = [];
        intervalsByCalendar.set(calendar, data);
      }
      data.push.apply(data, interval.intervals);
    });
    intervalsByCalendar.forEach((intervals, calendar) => {
      const unique = stripDuplicates(intervals);
      unique.sort(IntervalSorterFn);
      intervalsByCalendar.set(calendar, unique);
    });
    return this.intervalsByCalendar = intervalsByCalendar;
  }
};

// ../Engine/lib/Engine/calendar/CalendarCacheMultiple.js
var CalendarCacheMultiple = class extends CalendarCache {
  constructor(config) {
    super(config);
    this.calendarCaches = stripDuplicates(this.calendarCaches);
    this.intervalCache = new IntervalCache({
      emptyInterval: new CalendarCacheIntervalMultiple(),
      combineIntervalsFn: (interval1, interval2) => {
        return interval1.combineWith(interval2);
      }
    });
  }
  fillCache(startDate, endDate) {
    this.calendarCaches.forEach((calendarCache) => {
      calendarCache.fillCache(startDate, endDate);
      this.includeWrappingRangeFrom(calendarCache, startDate, endDate);
    });
  }
};
var COMBINED_CALENDARS_CACHE = /* @__PURE__ */ new Map();
var combineCalendars = (calendars) => {
  const uniqueOnly2 = stripDuplicates(calendars);
  if (uniqueOnly2.length === 0)
    throw new Error("No calendars to combine");
  uniqueOnly2.sort((calendar1, calendar2) => {
    if (calendar1.internalId < calendar2.internalId)
      return -1;
    else
      return 1;
  });
  const hash = uniqueOnly2.map((calendar) => calendar.internalId + "/").join("");
  const versionsHash = uniqueOnly2.map((calendar) => calendar.version + "/").join("");
  let cached = COMBINED_CALENDARS_CACHE.get(hash);
  let res;
  if (cached && cached.versionsHash === versionsHash)
    res = cached.cache;
  else {
    res = new CalendarCacheMultiple({ calendarCaches: uniqueOnly2.map((calendar) => calendar.calendarCache) });
  }
  return res;
};

// lib/Calendar/column/AgendaColumn.js
var AgendaColumn = class extends Column {
  static get defaults() {
    return {
      id: "agenda",
      text: "Agenda",
      filterable: false,
      sortable: false,
      editor: false,
      searchable: false,
      // With its defaultRenderer, and no eventRenderer, AgendaColumn measures
      // once then is able to calculate row heights from the cell's event count.
      // This field is automatically set to `true` if a column renderer or eventRenderer
      // is found.
      autoHeight: false,
      alwaysClearCell: false
    };
  }
  //endregion
  construct() {
    super.construct(...arguments);
    this.grid.ion({
      beforeResponsiveStateChange: "onBeforeGridResponsiveStateChange",
      cellMapPopulated: "onGridCellMapPopulated",
      thisObj: this
    });
  }
  //region Render
  /**
   * Renderer that shows a list of events for a single date.
   *
   * Note that all {@link Calendar.widget.AgendaView} columns are automatically set to
   * {@link Grid.column.Column#config-autoHeight} because an unpredictable number of events may appear in
   * any cell.
   *
   * @param {Object} renderData Object containing renderer parameters
   * @param {HTMLElement} renderData.cellElement Cell element, for adding CSS classes, styling etc. Can be `null` in case of export
   * @param {*} renderData.value Value to be displayed in the cell
   * @param {DayCell} renderData.record A {@link Calendar.widget.mixin.DayCellCollecter#typedef-DayCell} context object
   *   which contains contextual information about the date being rendered and an `events` property encapsulating
   *   all the events which coincide with the date.
   * @param {Grid.column.Column} renderData.column This column
   * @param {Grid.view.Grid} renderData.grid This grid
   * @param {Grid.row.Row} renderData.row Row object. Can be null in case of export. Use the
   *   {@link Grid.row.Row#function-assignCls row's API} to manipulate CSS class names.
   * @param {Boolean} renderData.isExport `true` if the record is being exported to Excel or a textual format, enabling special handling during export.
   * @param {Boolean} renderData.isMeasuring True if the column is being measured for a `resizeToFitContent` call.
   *   In which case an advanced renderer might need to take different actions.
   * @returns {DomConfig|String|null}
   */
  defaultRenderer({ cellElement: targetElement, row, record, grid, size }) {
    var _a5;
    const me = this, {
      events,
      date: date2
    } = record, { previousEvents } = record.data, children = [];
    for (let i = 0; i < events.length; i++) {
      const eventRecord = events[i], eventEndDate = eventRecord.endDate || DateHelper.add(eventRecord.startDate, eventRecord.duration, eventRecord.durationUnit), isOverflow = eventRecord.startDate < date2, overflows = eventEndDate > record.tomorrow, eventDomConfig = grid.createEventDomConfig({
        date: record.date,
        isAllDay: eventRecord.allDay || overflows || isOverflow,
        eventRecord
      });
      eventDomConfig.className["b-continues-future"] = overflows;
      eventDomConfig.className["b-continues-past"] = isOverflow;
      children.push({
        className: {
          "b-cal-agenda-event-row": 1
        },
        dataset: {
          rowId: eventRecord.id
        },
        children: [
          grid.callback("eventListEventTimeRenderer", grid, [eventRecord, date2]),
          eventDomConfig
        ],
        // Match existing data-rowId elements first and ensure DOM order matches
        // children order.
        syncOptions: {
          syncIdField: "rowId",
          releaseThreshold: 0,
          strict: true
        }
      });
    }
    row[previousEvents && previousEvents.date.getMonth() !== date2.getMonth() ? "addCls" : "removeCls"]("b-new-month");
    (_a5 = grid._cellRenderer) == null ? void 0 : _a5.call(grid, ...arguments);
    targetElement.classList[record.isNonWorking ? "add" : "remove"](grid.nonWorkingDayCls);
    DomSync.sync({
      domConfig: {
        onlyChildren: true,
        children: [
          grid.callback("agendaEventDateRenderer", grid, [date2]),
          {
            className: {
              "b-cal-event-bar-container": 1
            },
            children
          }
        ],
        // Match existing data-row-id elements first and ensure DOM order matches
        // children order.
        syncOptions: {
          syncIdField: "date",
          releaseThreshold: 0,
          strict: true
        }
      },
      targetElement
    });
    targetElement.setAttribute("aria-label", `${DateHelper.format(date2, "dddd MMMM Do, YYYY")}, ${grid.L("eventCount", events.length)}`);
    targetElement.setAttribute("tabIndex", -1);
    targetElement.dataset.date = record.id;
    if (!me.autoHeight) {
      const dateBlock = targetElement.querySelector(".b-cal-agenda-date"), cellTbPadding = me._cellTbPadding != null ? me._cellTbPadding : me._cellTbPadding = DomHelper.getEdgeSize(targetElement, "padding", "tb").height, containerPadding = me._containerPadding != null ? me._containerPadding : me._containerPadding = DomHelper.getEdgeSize(targetElement.querySelector(".b-cal-event-bar-container"), "margin", "tb").height, dateBlockHeight = me._dateBlockHeight != null ? me._dateBlockHeight : me._dateBlockHeight = Rectangle.outer(dateBlock).height;
      size.height = Math.max(record.events.length * (grid.eventHeightInPixels + grid.eventRowSpacing) - grid.eventRowSpacing + cellTbPadding + containerPadding + (grid.responsiveState === "small" ? dateBlockHeight : 0), 86);
    }
  }
  onBeforeGridResponsiveStateChange() {
    this._cellTbPadding = this._containerPadding = this._dateBlockHeight = this._dateBlockHeight = null;
  }
  onGridCellMapPopulated() {
    this._dateBlockHeight = null;
  }
  getCellClass(cellContext) {
    const result = super.getCellClass(cellContext);
    result["b-sticky-cell"] = result[`b-day-of-week-${cellContext.record.day}`] = 1;
    return result;
  }
  //endregion
  //region Other
  headerRenderer({ headerElement: targetElement }) {
    targetElement.innerHTML = "";
    DomSync.sync({
      domConfig: {
        className: {
          "b-cal-agenda-header": 1,
          "b-grid-header": 1,
          "b-last-leaf": 1
        },
        children: [{
          className: {
            "b-cal-agenda-header-date": 1
          },
          text: "Date"
        }, {
          className: {
            "b-cal-agenda-header-time": 1
          },
          text: "Time"
        }, {
          className: {
            "b-cal-agenda-header-event": 1
          },
          text: "Event"
        }]
      },
      targetElement
    });
  }
  //endregion
};
//region Config
__publicField(AgendaColumn, "$name", "AgendaColumn");
__publicField(AgendaColumn, "type", "agendacolumn");
ColumnStore.registerColumnType(AgendaColumn);
AgendaColumn._$name = "AgendaColumn";

// lib/Calendar/column/EventColumn.js
var EventColumn = class extends Column {
  static get defaults() {
    return {
      id: "calendarevents",
      text: "Event Details",
      filterable: false,
      sortable: false,
      editor: false,
      searchable: false,
      autoHeight: true,
      alwaysClearCell: false
    };
  }
  //endregion
  //region Render
  /**
   * Renderer that shows resource avatars of assigned resources next to event names for a single date.
   *
   * <div class="note">
   * You should never modify any records inside this method.
   * </div>
   * Note that all {@link Calendar.widget.AgendaView} columns are automatically set to
   * {@link Grid.column.Column#config-autoHeight} because an unpredictable number of events may appear in
   * any cell.
   *
   * @config {Function} renderer
   * @param {Object} renderData Object containing renderer parameters
   * @param {HTMLElement} renderData.cellElement Cell element, for adding CSS classes, styling etc. Can be `null` in case of export
   * @param {*} renderData.value Value to be displayed in the cell
   * @param {DayCell} renderData.record A {@link Calendar.widget.mixin.DayCellCollecter#typedef-DayCell} context object
   *   which contains contextual information about the date being rendered and an `events` property encapsulating
   *   all the events which coincide with the date.
   * @param {Grid.column.Column} renderData.column This column
   * @param {Grid.view.Grid} renderData.grid This grid
   * @param {Grid.row.Row} renderData.row Row object. Can be null in case of export. Use the
   *   {@link Grid.row.Row#function-assignCls row's API} to manipulate CSS class names.
   * @param {Boolean} renderData.isExport `true` if the record is being exported to Excel or a textual format, enabling special handling during export.
   * @param {Boolean} renderData.isMeasuring True if the column is being measured for a `resizeToFitContent` call.
   *   In which case an advanced renderer might need to take different actions.
   * @returns {DomConfig|String|null}
   */
  renderer({ cellElement: targetElement, record, grid }) {
    var _a5, _b2;
    const resourceFilter = (_a5 = grid.calendar) == null ? void 0 : _a5.widgetMap.resourceFilter, resources = resourceFilter ? record.resources.filter((r) => resourceFilter.selected.includes(r)) : record.resources;
    (_b2 = grid._cellRenderer) == null ? void 0 : _b2.call(grid, ...arguments);
    grid.showResourceAvatars = true;
    DomSync.sync({
      domConfig: {
        onlyChildren: true,
        children: [
          {
            class: "b-cal-event-resource-avatars",
            children: resources.map((resource) => {
              return {
                class: "b-cal-event-resource-avatar-row",
                children: [
                  grid.getResourceAvatar(resource),
                  {
                    class: "b-cal-event-resource-avatar-desc",
                    children: [{
                      class: "b-event-column-resource-name",
                      text: resource.name
                    }, {
                      class: "b-event-column-resource-role",
                      text: resource.role
                    }]
                  }
                ]
              };
            })
          },
          {
            class: "b-cal-event-column-event-desc",
            children: [{
              class: "b-cal-event-column-event-name",
              text: record.name
            }]
          },
          {
            class: "b-cal-event-column-times",
            children: [grid.eventListEventTimeRenderer(record)]
          }
        ],
        // Match existing data-row-id elements first and ensure DOM order matches
        // children order.
        syncOptions: {
          syncIdField: "date",
          releaseThreshold: 0,
          strict: true
        }
      },
      targetElement
    });
  }
  //endregion
};
//region Config
__publicField(EventColumn, "$name", "EventColumn");
__publicField(EventColumn, "type", "calendarevents");
ColumnStore.registerColumnType(EventColumn);
EventColumn.exposeProperties();
EventColumn._$name = "EventColumn";

// lib/Calendar/model/TimeRangeModel.js
var TimeRangeModel2 = class extends TimeRangeModel {
  //endregion
};
/**
 * @hidefields children, parentId, parentIndex
 */
//region Fields
__publicField(TimeRangeModel2, "fields", [
  /**
   * The side of the time range on which the header will be displayed The default value is `'start'`.
   *
   * This value determines where to place the time range header relative to events. A value of `'start'` will
   * place the header on the left side, while `'end'` will place the time range on the right side (these will
   * be reversed in RTL mode).
   *
   * @field {'start'|'end'} alignment
   * @category Styling
   * @default 'start'
   */
  {
    name: "alignment",
    defaultValue: "start",
    internal: true
  },
  /**
   * The primary color of the header element.
   * @field {'red'|'pink'|'purple'|'violet'|'indigo'|'blue'|'cyan'|'teal'|'magenta'|'green'|'lime'|'yellow'|'orange'|'deep-orange'|'gray'|'gantt-green'|'light-gray'} color
   * @category Styling
   */
  {
    name: "color",
    internal: true
  },
  /**
   * The text for the footer.
   * @field {String} footer
   * @category Styling
   */
  {
    name: "footer",
    internal: true
  },
  /**
   * The direction to rotate header/footer text.
   * @field {'neg'|'pos'} rotation
   * @category Styling
   */
  {
    name: "rotation",
    internal: true
  }
]);
TimeRangeModel2._$name = "TimeRangeModel";

// lib/Calendar/data/TimeRangeStore.js
var TimeRangeStore2 = class extends TimeRangeStore {
};
/**
 * @hideconfigs lazyLoad
 */
/**
 * @hideproperties lazyLoad
 */
/**
 * @hideevents lazyLoadStarted, lazyLoadEnded
 */
__publicField(TimeRangeStore2, "defaultConfig", {
  modelClass: TimeRangeModel2
});
TimeRangeStore2.initClass();
TimeRangeStore2._$name = "TimeRangeStore";

// lib/Calendar/model/ProjectModel.js
var ProjectModel2 = class extends ProjectModel {
  //endregion
};
__publicField(ProjectModel2, "$name", "ProjectModel");
//region Configs
__publicField(ProjectModel2, "configurable", {
  timeRangeStore: {
    modelClass: TimeRangeModel2
  }
});
__publicField(ProjectModel2, "defaultConfig", {
  /**
   * The constructor of the time range model class, to be used in the project. Will be set as the
   * {@link Core.data.Store#config-modelClass modelClass} property of the {@link #property-timeRangeStore}
   *
   * @config {Calendar.model.TimeRangeModel}
   * @typings {typeof TimeRangeModel}
   * @category Models & Stores
   */
  timeRangeModelClass: TimeRangeModel2,
  /**
   * The constructor to create an timeRange store instance with. Should be a class, subclassing the
   * {@link Core.data.Store}
   * @config {Core.data.Store|Object}
   * @typings {typeof Store|object}
   * @category Models & Stores
   */
  timeRangeStoreClass: TimeRangeStore2
});
ProjectModel2.applyConfigs = true;
ProjectModel2.initClass();
ProjectModel2._$name = "ProjectModel";

// lib/Calendar/data/CrudManager.js
var CrudManager2 = class extends CrudManager {
  //endregion
  buildProjectConfig() {
    const ret = super.buildProjectConfig(), { timeRangeStore } = this;
    if (timeRangeStore) {
      ret.timeRangeStore = timeRangeStore;
    }
    return ret;
  }
  //region Stores
  get timeRangeStore() {
    return super.timeRangeStore;
  }
  set timeRangeStore(store) {
    var _a5;
    if (store && ObjectHelper.isObject(store)) {
      store = ObjectHelper.assign({
        modelClass: this.timeRangeClass
      }, store);
    }
    this.setFeaturedStore("_timeRangeStore", store, (_a5 = this.project) == null ? void 0 : _a5.timeRangeStoreClass);
  }
  //endregion
};
__publicField(CrudManager2, "$name", "CrudManager");
//region Config
__publicField(CrudManager2, "configurable", {
  projectClass: ProjectModel2,
  timeRangeClass: TimeRangeModel2
});
CrudManager2.initClass();
CrudManager2._$name = "CrudManager";

// lib/Calendar/feature/CalendarFeature.js
var CalendarFeature = class extends InstancePlugin.mixin(Factoryable_default) {
  static get factoryable() {
    return {
      // establish this class as the Factoryable base
    };
  }
  getViewZoneType(view) {
    const { zoneTypes } = this;
    let ret;
    if (zoneTypes && (ret = zoneTypes[view.type]) === void 0) {
      if (view.isWeekView && zoneTypes.week !== void 0) {
        ret = zoneTypes.week;
      } else if (view.isDayView) {
        ret = zoneTypes.day;
      } else if (view.isMonthView) {
        ret = zoneTypes.month;
      } else if (view.isYearView) {
        ret = zoneTypes.year;
      } else if (view.isResourceView) {
        ret = zoneTypes.resource;
      } else if (view.isAgendaView) {
        ret = zoneTypes.agenda;
      }
    }
    return ret;
  }
  updateDisableOnReadOnly(disable) {
    const me = this, { client } = me, name = "readonlyToggle", handler = "onClientReadOnlyToggle";
    if (disable) {
      me[handler]({ readOnly: client.readOnly });
      client.ion({
        name,
        readOnly: handler,
        thisObj: me
      });
    } else {
      me.detachListeners(name);
    }
  }
  doDestroy() {
    this.syncModes();
    super.doDestroy();
  }
  createZone(type, config) {
    return new type({
      owner: this,
      ...config
    });
  }
  onClientReadOnlyToggle({ readOnly }) {
    this.disabled = readOnly;
  }
  updateDisabled(disabled, was) {
    super.updateDisabled(disabled, was);
    if (this.client.isPainted) {
      this.syncModes();
    }
  }
  onCalendarPaint({ firstPaint }) {
    if (firstPaint) {
      this.syncModes();
    }
  }
  syncModes() {
    const me = this, { client, _modeDetacher, zones } = me;
    let newZones = null, modes, name, view, type, zone;
    if (me.disabled || me.isDestroying || !me.zoneTypes) {
      _modeDetacher == null ? void 0 : _modeDetacher();
    } else {
      if (client.isCalendar) {
        if (!_modeDetacher) {
          me._modeDetacher = client.ion({
            addMode: () => me.syncModes()
          });
        }
        modes = client.modes;
      } else {
        modes = {
          view: client
        };
      }
      for (name in modes) {
        zone = zones == null ? void 0 : zones[name];
        view = modes[name];
        type = me.getViewZoneType(view);
        if (type) {
          if ((ObjectHelper.isObject(zone) ? zone.type : zone == null ? void 0 : zone.constructor) === type) {
            delete zones[name];
          } else if (view.isPainted) {
            zone = me.createZone(type, {
              view
            });
          } else {
            zone = me.whenPainted(name, view, type);
          }
          (newZones || (newZones = {}))[name] = zone;
        }
      }
    }
    me.zones = newZones;
    if (zones) {
      for (name in zones) {
        zones[name].destroy();
      }
    }
  }
  whenPainted(name, view, type) {
    const me = this, proxy = {
      type,
      destroy: view.ion({
        once: true,
        // Must create zone first
        prio: 1e4,
        paint() {
          if (me.zones[name] === proxy) {
            me.zones[name] = me.createZone(type, {
              view
            });
          }
        }
      })
    };
    return proxy;
  }
};
__publicField(CalendarFeature, "configurable", {
  disableOnReadOnly: null,
  zoneTypes: null
});
__publicField(CalendarFeature, "properties", {
  clientListeners: {
    paint: "onCalendarPaint"
  }
});
CalendarFeature._$name = "CalendarFeature";

// lib/Calendar/feature/CalendarZone.js
var CalendarZone = class extends Base {
  updateView(view) {
    const me = this, listeners = view && me.viewListeners;
    me.detachListeners("viewListeners");
    listeners && (view == null ? void 0 : view.ion(ObjectHelper.merge({
      thisObj: me,
      name: "viewListeners"
    }, listeners)));
  }
};
__publicField(CalendarZone, "$name", "CalendarZone");
__publicField(CalendarZone, "configurable", {
  /**
   * The owning feature of this zone instance.
   * @config {Calendar.feature.CalendarFeature}
   * @readonly
   */
  owner: null,
  /**
   * The associated view of this zone instance.
   * @config {Calendar.widget.mixin.CalendarMixin}
   * @readonly
   */
  view: {
    $config: "nullify",
    value: null
  },
  /**
   * A listener config object to attach to this object's {@link #config-view}
   * @config {Object}
   */
  viewListeners: null
});
CalendarZone.initClass();
CalendarZone._$name = "CalendarZone";

// lib/Calendar/widget/ResourceChipView.js
var ResourceChipView = class extends ChipView {
  getItemCls(r) {
    return DomHelper.isNamedColor(r.eventColor) ? ` b-sch-${r.eventColor}` : "";
  }
  getItemStyle(r) {
    return `color:#fff;${r.eventColor && !DomHelper.isNamedColor(r.eventColor) ? `background-color:${r.eventColor}` : ""}`;
  }
  iconTpl(resourceRecord) {
    return DomHelper.createElement(this.owner.ownerFeature.client.activeView.getResourceAvatar(resourceRecord)).outerHTML;
  }
};
__publicField(ResourceChipView, "$name", "ResourceChipView");
// Factoryable type name
__publicField(ResourceChipView, "type", "resourcechipview");
__publicField(ResourceChipView, "configurable", {
  navigator: null,
  itemsFocusable: false,
  closable: false,
  scrollable: false,
  itemTpl(resource) {
    var _a5;
    return DomHelper.createElement({
      className: "b-cal-event-resource-avatar-desc",
      children: [{
        class: "b-event-column-resource-name",
        text: resource.name
      }, ((_a5 = resource.role) == null ? void 0 : _a5.length) ? {
        class: "b-event-column-resource-role",
        text: resource.role
      } : null]
    }).outerHTML;
  }
});
ResourceChipView.initClass();
ResourceChipView._$name = "ResourceChipView";

// lib/Calendar/widget/mixin/EventRenderer.js
var emptyString3 = new String("");
var EventRenderer_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    // Must refresh when renderer changes
    updateEventRenderer() {
      this.refreshSoon();
    }
    /**
     * This is the standard way to create a {@link Core.helper.DomHelper#typedef-DomConfig}
     * element definition object for creating event bars in all view types.
     *
     * This may be used by application code which needs to create DOM structure for event bars, such
     * as in custom cell renderers in an {@link Calendar.widget.AgendaView}.
     *
     * @param {Object} renderData Context for the event bar config creation.
     * @param {Scheduler.model.EventModel} renderData.eventRecord The event record to create a {@link DomConfig} block for.
     * @param {Boolean} renderData.minimal If this is set, no inner content is rendered, only
     * the wrap and body element. This is to enable rendering placeholders such as bullets
     * to represent the presence of events.
     * @param {Date} renderData.date The date to create th DOM config for.
     * @param {Date} [renderData.eventEndDate] An optional override to the event's ending date.
     * @param {Function} [eventRenderer] Optionally a function which created content HTML for the
     * event body. Defaults to any {@link #config-eventRenderer} configured into this view.
     * @returns {DomConfig} A {@link Core.helper.DomHelper#typedef-DomConfig} element definition object
     */
    createEventDomConfig(renderData, eventRenderer = this.eventRenderer) {
      var _a6, _b2, _c2, _d, _e, _f, _g, _h;
      const me = this, {
        intradayCls,
        alldayCls,
        solidBarCls,
        pastEventCls,
        showTime,
        timeFormat,
        eventHeaderRenderer,
        rtl
      } = me, calendar = me.up("calendar"), {
        eventRecord,
        minimal
      } = renderData, eventHeight = minimal ? "auto" : me.eventHeight, eventEndDate = renderData.eventEndDate || eventRecord.endingDate, resourceRecord = me.callback(me.getPrimaryResource, me, [eventRecord.isOccurrence ? eventRecord.recurringEvent : eventRecord]), isRecurring = eventRecord.isRecurring || eventRecord.isOccurrence, isAllDay = "isAllDay" in renderData ? renderData.isAllDay : (_a6 = me.isAllDayEvent) == null ? void 0 : _a6.call(me, eventRecord), eventInnerStyle = {
        height: eventHeight !== "auto" ? DomHelper.setLength(eventHeight) : null
      }, eventSelectedCls = calendar == null ? void 0 : calendar.eventSelectedCls, activeCls = (_b2 = calendar == null ? void 0 : calendar.navigator) == null ? void 0 : _b2.focusCls, dataset = renderData.dataset = {
        eventId: eventRecord.id
      }, eventStyle = Object.assign(DomHelper.parseStyle(resourceRecord == null ? void 0 : resourceRecord.eventStyle), DomHelper.parseStyle(eventRecord.style)), eventConfig = {
        className: "b-cal-event",
        style: eventInnerStyle
      };
      let color = renderData.eventColor || eventRecord.color || eventRecord.eventColor || (resourceRecord == null ? void 0 : resourceRecord.eventColor) || emptyString3;
      renderData.wrapperChildren = [eventConfig];
      renderData.cls = eventRecord.cls.clone();
      renderData.solidBar = isAllDay || minimal;
      renderData.iconStyle = me.iconTarget === "header" && me.showTime && !DomHelper.isNamedColor(color) ? { color } : {};
      renderData.iconCls = new DomClassList(eventRecord.iconCls);
      renderData.style = eventStyle;
      renderData.eventColor = color;
      renderData.eventHeight = eventHeight;
      renderData.cls[me.shortEventCls] = eventRecord.rawDurationMS <= me.shortEventDuration;
      renderData.bodyStyle = {};
      renderData.resourceRecord = resourceRecord;
      renderData.view = me;
      if (resourceRecord == null ? void 0 : resourceRecord.cls) {
        renderData.cls.add(resourceRecord.cls);
      }
      let bodyContent = (_c2 = me.internalBodyContentRenderer(eventRecord, resourceRecord, renderData, !minimal && eventRenderer)) != null ? _c2 : "";
      const isStringContent = typeof bodyContent === "string", complexContent = isStringContent ? StringHelper.isHtml(bodyContent) : true;
      if (eventRenderer && !minimal) {
        if (renderData.eventHeight !== eventHeight) {
          eventInnerStyle.height = DomHelper.setLength(renderData.eventHeight);
        }
        if (typeof renderData.cls === "string") {
          renderData.cls = new DomClassList(renderData.cls);
        }
        if (typeof renderData.iconCls === "string") {
          renderData.iconCls = new DomClassList(renderData.iconCls);
        }
        if (typeof renderData.style === "string") {
          renderData.style = DomHelper.parseStyle(renderData.style);
        }
        if (typeof renderData.bodyStyle === "string") {
          renderData.bodyStyle = DomHelper.parseStyle(renderData.bodyStyle);
        }
      }
      bodyContent = [{
        className: {
          "b-cal-event-desc": 1,
          "b-cal-event-desc-complex": complexContent
        },
        [isStringContent ? "html" : "children"]: bodyContent
      }];
      Object.assign(renderData.cls, {
        "b-cal-event-wrap": 1,
        "b-iscreating": eventRecord.isCreating,
        "b-readonly": eventRecord.readOnly,
        "b-minimal": minimal,
        "b-milestone": eventRecord.isMilestone && !isAllDay,
        [alldayCls]: alldayCls && isAllDay,
        [solidBarCls]: solidBarCls && renderData.solidBar,
        [intradayCls]: intradayCls && !isAllDay,
        [pastEventCls]: pastEventCls && eventEndDate < /* @__PURE__ */ new Date(),
        [eventSelectedCls]: (calendar == null ? void 0 : calendar.isEventSelected(eventRecord)) || renderData.asSelected,
        "b-rtl": rtl,
        [activeCls]: ((_d = calendar == null ? void 0 : calendar.navigator.activeItem) == null ? void 0 : _d.dataset.eventId) === eventRecord.id
      });
      color = renderData.eventColor;
      const { bodyColor } = renderData;
      if (bodyColor == null ? void 0 : bodyColor.length) {
        renderData.cls["b-custom-body-color"] = 1;
        renderData.bodyStyle["--dayview-body-background-color"] = bodyColor;
      }
      if (color == null ? void 0 : color.length) {
        eventStyle["--cal-event-color"] = DomHelper.createColorStyle(color);
      }
      if (eventHeaderRenderer) {
        const eventHeader = me.callback(eventHeaderRenderer, me, [{
          view: me,
          eventRecord,
          resourceRecord,
          renderData
        }]), isArray4 = Array.isArray(eventHeader);
        if (!isArray4 && typeof eventHeader === "object") {
          eventHeader.className = DomClassList.normalize(eventHeader.className, "object");
          eventHeader.className["b-event-header"] = 1;
          bodyContent.unshift(eventHeader);
        } else {
          bodyContent.unshift({
            className: "b-event-header",
            [isArray4 ? "children" : eventHeader.includes("<") ? "html" : "text"]: eventHeader
          });
        }
      } else if (showTime && !(isAllDay && me.showAllDayHeader)) {
        bodyContent.unshift({
          className: "b-event-header",
          children: [{
            className: "b-event-time",
            html: me.renderTimes(eventRecord, showTime, timeFormat)
          }]
        });
      }
      const hasIcon = Boolean((_e = renderData.iconCls) == null ? void 0 : _e.length), useIconAsRecurrIcon = !hasIcon && isRecurring, showBullet = "showBullet" in renderData ? renderData.showBullet : me.showBullet === true || (((_f = me.showBullet) == null ? void 0 : _f.bar) && renderData.solidBar || ((_g = me.showBullet) == null ? void 0 : _g.noBar) && !renderData.solidBar), showCircle = !hasIcon && !isRecurring && showBullet, iconElement = {
        tag: "i",
        className: Object.assign({
          "b-cal-event-icon": !useIconAsRecurrIcon,
          "b-cal-recurrence-icon": useIconAsRecurrIcon,
          "b-icon": 1,
          "b-fw-icon": 1,
          "b-icon-circle": showCircle,
          "b-icon-recurring": useIconAsRecurrIcon
        }, renderData.iconCls),
        style: renderData.iconStyle
      }, eventInnerContent = [{
        className: "b-cal-event-body",
        children: bodyContent,
        style: renderData.bodyStyle
      }], iconParent = me.iconTarget === "header" && me.showTime ? ((_h = bodyContent[0].children) == null ? void 0 : _h.length) > 0 ? bodyContent[0].children : bodyContent : eventInnerContent;
      if (hasIcon || useIconAsRecurrIcon || showCircle) {
        if (me.iconTarget === "header" && me.showTime) {
          iconParent.push(iconElement);
        } else {
          iconParent.unshift(iconElement);
        }
      }
      if (hasIcon && isRecurring) {
        iconParent.push({
          tag: "i",
          className: {
            "b-cal-recurrence-icon": 1,
            "b-icon": 1,
            "b-fw-icon": 1,
            "b-icon-recurring": 1
          },
          style: renderData.iconStyle
        });
      }
      if (!minimal) {
        eventConfig.children = eventInnerContent;
      }
      const {
        startText,
        endText
      } = Calendar.getStartAndEndDates(me, eventRecord), result = {
        role: "button",
        "aria-label": eventRecord.name + "," + (endText ? `${startText} ${me.L("L{Object.to}")} ${endText}` : `${eventRecord.isMilestone ? me.L("L{Object.at}") : me.L("L{Object.on}")} ${startText}`) + ".",
        dataset,
        // Events are tabbable unless minimally rendered (dots)
        tabIndex: minimal ? null : 0,
        className: renderData.cls,
        style: renderData.style,
        children: renderData.wrapperChildren
      };
      calendar == null ? void 0 : calendar.onEventDataGenerated(renderData, result);
      return result;
    }
    renderTimes(eventRecord, { startTime, endTime }, timeFormat) {
      const showEndTime = !eventRecord.isMilestone && endTime;
      return `${startTime ? DateHelper.format(eventRecord.startDate, timeFormat) : ""}${startTime && showEndTime ? ` ${this.rtl ? "\u2190" : "\u2192"} ` : ""}${showEndTime ? DateHelper.format(eventRecord.endDate, timeFormat) : ""}`;
    }
    internalBodyContentRenderer(eventRecord, resourceRecord, renderData, eventRenderer) {
      var _a6;
      const me = this, { showResourceAvatars } = me, defaultBodyContent = eventRenderer && me.callback(eventRenderer, me, [{
        view: me,
        eventRecord,
        resourceRecord,
        renderData
      }]) || // If creating a DayView event block, we inject <br> for newlines
      StringHelper[me.eventHeight === "auto" ? "encodeHtmlBR" : "encodeHtml"](eventRecord.name), isString = typeof defaultBodyContent === "string", isArray4 = Array.isArray(defaultBodyContent);
      if (showResourceAvatars && ((_a6 = eventRecord.assigned) == null ? void 0 : _a6.size)) {
        const content = [{
          class: "b-cal-event-resource-avatars",
          children: eventRecord.resources.map((resource) => me.getResourceAvatar(resource))
        }], spliceArgs = [showResourceAvatars === "last" ? 0 : 1, 0];
        if (typeof defaultBodyContent === "string") {
          spliceArgs.push({
            class: "b-cal-event-name",
            text: defaultBodyContent
          });
        } else {
          spliceArgs.push[isArray4 ? "apply" : "call"](spliceArgs, defaultBodyContent);
        }
        content.splice(...spliceArgs);
        return content;
      } else {
        return isArray4 || isString ? defaultBodyContent : [defaultBodyContent];
      }
    }
  }, __publicField(_a5, "$name", "EventRenderer"), __publicField(_a5, "configurable", {
    /**
     * A function, or the name of a function in the ownership hierarchy which you
     * can specify to customize event DOM content.
     *
     * This function is called each time an event is rendered to allow developers to mutate
     * the cell metadata, or the CSS classes to be applied to the event element.
     *
     * It's called with the event record, and a eventData object which allows you to mutate event metadata
     * such as `cls`, `style`.
     *
     * The `cls` property is an object whose property names will be added to the event element if the property
     * value is truthy.
     *
     * The `style` property is an object containing style properties for the event element.
     *
     * A non-null return value from the renderer is used as the event body content. A nullish
     * return value causes the default renderer to be used which just uses the event name.
     *
     * If a string is returned, it is used as the HTML content of the event body element.
     *
     * If an object is returned, it is used as a {@link Core.helper.DomHelper#typedef-DomConfig} object to
     * create complex content in the event body element.
     *
     * <div class="note">
     * You should never modify any records inside this method.
     * </div>
     *
     * ```javascript
     *  eventRenderer({ eventRecord, renderData }) {
     *      if (eventRecord.name === 'Doctors appointment') {
     *          renderData.style.fontWeight = 'bold';
     *          renderData.cls['custom-cls'] = 1;
     *
     *          return 'Special doctors appointment';
     *      }
     *  }
     * ```
     *
     * IMPORTANT: When returning content, be sure to consider how that content should be encoded to avoid XSS
     * (Cross-Site Scripting) attacks. This is especially important when including user-controlled data such as
     * the event's `name`. The function {@link Core.helper.StringHelper#function-encodeHtml-static} as well as
     * {@link Core.helper.StringHelper#function-xss-static} can be helpful in these cases.
     *
     * For example:
     *
     * ```javascript
     *  eventRenderer({ eventRecord }) {
     *      return StringHelper.xss`Event: ${eventRecord.name}`;
     *  }
     * ```
     *
     * @param {Object} detail An object that contains data about the event being rendered
     * @param {Calendar.widget.mixin.CalendarMixin} detail.view The view rendering the event
     * @typings detail.view -> {typeof CalendarMixin}
     * @param {Scheduler.model.EventModel} detail.eventRecord The event record
     * @param {Scheduler.model.ResourceModel} detail.resourceRecord The Resource record
     * @param {Object} detail.renderData A data object containing properties that will be used to create the event element
     * @param {Object} detail.renderData.style The style property is an object containing style properties for the event element
     * @param {Object} detail.renderData.cls The cls property is an object whose property names will be added to
     * the event element if the property value is truthy
     * @param {Object} detail.renderData.iconStyle The iconStyle property is an object containing style properties for
     * the icon element if an icon element is to be used
     * @param {Object} detail.renderData.iconCls The iconCls property is an object whose property names will be added to
     * the icon element. Initially set from the event record's {@link Scheduler.model.EventModel#field-iconCls}.
     * Can be mutated by the renderer. If null, or no properties are set, no icon will be rendered
     * @param {String} detail.renderData.eventColor Color to be applied to the event
     * @param {Object} detail.renderData.dataset An object which produces the `dataset` of the resulting event bar
     * @param {Boolean} detail.renderData.solidBar This is valid for views which create event bars.
     * This is set to `true` by default for all day and interday events so that these appear as a solid block of background color.
     * An eventRenderer may mutate this flag to change in what manner the event bar is coloured - as a solid bar of colour,
     * or using the foreground colour (text and icons) such as the MonthView, the CalendarRow (all day events in a DayView), and OverflowPopups
     * @param {String} detail.renderData.bodyColor When used in a {@link Calendar.widget.DayView},
     * this color is applied to the body of the event element. Note that this must be light enough
     * that the text colour (From the SASS variable `$dayview-event-color`) is visible
     * @param {Boolean} detail.renderData.showBullet If there is no `iconCls`, and the event is not recurring, then by default
     * a "bullet" circular icon is shown if the view's {@link Calendar.widget.mixin.CalendarMixin#config-showBullet}
     * if set. Setting this property in an event renderer overrides this behaviour.
     * @returns {DomConfig|DomConfig[]|String|null}
     * @config {Function|String}
     */
    eventRenderer: null,
    /**
     * A function, or the name of a function in the ownership hierarchy which you
     * can specify to customize event DOM content.
     *
     * This function is called each time an event is rendered to produce the header DOM
     * structure which precedes the body content (Event name).
     *
     * __In {@link Calendar.widget.DayView}__, the default event header is the event's
     * {@link Scheduler.model.EventModel#field-startDate} formatted according to the
     * {@link Calendar.widget.mixin.CalendarMixin#config-timeFormat}
     *
     * Other views produce horizontal event bars, and do not have a seperate event header element.
     *
     * Default behaviour can be overridden by specifiying this property.
     *
     * For example:
     *
     * ```javascript
     *  eventHeaderRenderer({ eventRecord }) {
     *      return [{
     *          class : 'flexible-start',
     *          text  : DateHelper.format(eventRecord.startDate, '{Around} hh:mm')
     *      }, {
     *          class : 'status-indicator b-fa',
     *          text  : eventRecord.completed ? '\xf00c' : '\xf00d'
     *      }];
     *  }
     * ```
     *
     * @param {Object} detail An object that contains data about the event being rendered
     * @param {Calendar.widget.mixin.CalendarMixin} detail.view The view rendering the event
     * @typings detail.view -> {typeof CalendarMixin}
     * @param {Scheduler.model.EventModel} detail.eventRecord The event record
     * @param {Scheduler.model.ResourceModel} detail.resourceRecord The event record
     * @param {Object} detail.renderData A data object containing properties that will be used to create the event element
     * @param {Object} detail.renderData.style The style property is an object containing style properties for the event element
     * @param {Object} detail.renderData.cls The cls property is an object whose property names will be added to
     * the event element if the property value is truthy
     * @param {Object} detail.renderData.iconStyle The iconStyle property is an object containing style properties for
     * the icon element if an icon element is to be used
     * @param {Object} detail.renderData.iconCls The iconCls property is an object whose property names will be added to
     * the icon element. Initially set from the event record's {@link Scheduler.model.EventModel#field-iconCls}.
     * Can be mutated by the renderer. If null, or no properties are set, no icon will be rendered
     * @param {String} detail.renderData.eventColor Color to be applied to the event
     * @param {Object} detail.renderData.dataset An object which produces the `dataset` of the resulting event bar
     * @param {Boolean} detail.renderData.solidBar This is valid for views which create event bars.
     * This is set to `true` by default for all day and interday events so that these appear as a solid block of background color.
     * An eventRenderer may mutate this flag to change in what manner the event bar is coloured - as a solid bar of colour,
     * or using the foreground colour (text and icons) such as the MonthView, the CalendarRow (all day events in a DayView), and OverflowPopups
     * @param {String} detail.renderData.bodyColor When used in a {@link Calendar.widget.DayView},
     * this color is applied to the body of the event element. Note that this must be light enough
     * that the text colour (From the SASS variable `$dayview-event-color`) is visible
     * @param {Boolean} detail.showBullet If there is no `iconCls`, and the event is not recurring, then by default
     * a "bullet" circular icon is shown if the view's {@link Calendar.widget.mixin.CalendarMixin#config-showBullet}
     * if set. Setting this property in an event renderer overrides this behaviour.
     * @returns {DomConfig|DomConfig[]|String|null}
     * @config {Function|String}
     */
    eventHeaderRenderer: null,
    /**
     * If this is set to `true`, then when determining which assigned resource of a multi assigned event
     * to use to create the event UI, the first resource which is still selected in the
     * {@link Calendar.widget.Sidebar#property-resourceFilter} is used.
     *
     * The default is to use the first resource in the assigned list.
     *
     * The resource's {@link Scheduler.model.ResourceModel#field-eventColor} is used as the colour for the
     * event unless the event has its own {@link Scheduler.model.EventModel#field-eventColor}.
     *
     * The resource's {@link Scheduler.model.ResourceModel#field-eventStyle} is used as the base of the style
     * for the event, and the event's {@link Scheduler.model.EventModel#field-style} is applied to it.
     * @prp {Boolean}
     */
    filterEventResources: null,
    /**
     * A Function (or name of a function in the ownership hierarchy) which returns the
     * {@link Scheduler.model.ResourceModel resource record} to use to create the UI for an event.
     *
     * The resource's {@link Scheduler.model.ResourceModel#field-eventColor} is used as the colour for the
     * event unless the event has its own {@link Scheduler.model.EventModel#field-eventColor}.
     *
     * The resource's {@link Scheduler.model.ResourceModel#field-eventStyle} is used as the base of the style
     * for the event, and the event's {@link Scheduler.model.EventModel#field-style} is applied to it.
     *
     * For views which are linked to a single resource such as `ResourceView` and `DayResourceView`, the
     * default implementation always returns the view's resource.
     *
     * If multi assignment is used, the default is to pick the first resource in the assigned resource list.
     *
     * If this view's {@link #property-filterEventResources} is `true`,
     * then the first assigned event which is still filtered __in__ is chosen.
     *
     * An implementation of this function may be configured in to your {@link Calendar.view.Calendar#config-modes}
     * to customize how the resource is chosen from a multi assigned event.
     *
     * @config {Function|String} [getPrimaryResource]
     * @param {Scheduler.model.EventModel} eventRecord The event from which to extract the primary resource.
     * @returns {Scheduler.model.ResourceModel} The resource to be used to render the event.
     */
    getPrimaryResource({ resources }) {
      var _a6, _b2;
      if (this.resource) {
        return this.resource;
      }
      if (resources.length) {
        const resourceFilter = this.filterEventResources && ((_b2 = (_a6 = this.calendar) == null ? void 0 : _a6.widgetMap) == null ? void 0 : _b2.resourceFilter);
        return resourceFilter ? resources.find((r) => resourceFilter.selected.includes(r)) : resources[0];
      }
    }
  }), _a5;
};

// lib/Calendar/widget/CalendarDatePicker.js
var emptyString4 = new String();
var CalendarDatePicker = class extends SchedulerDatePicker.mixin(EventRenderer_default) {
  construct(config) {
    var _a5;
    const calendar = this.calendar = (_a5 = config.owner || config.parent) == null ? void 0 : _a5.up("calendar");
    if (calendar && !config.eventStore) {
      config.eventStore = calendar.eventStore;
    }
    super.construct(...arguments);
  }
  changeEventDots(eventDots) {
    const me = this;
    return new Proxy(eventDots, {
      set(target) {
        const result = Reflect.set(...arguments);
        me.updateEventDots(target);
        return result;
      }
    });
  }
  updateEventDots({ marginTop, gap, size, stripe }) {
    const { style, classList } = this.weeksElement;
    style.setProperty("--event-count-margin-top", `${marginTop}px`);
    style.setProperty("--event-count-dot-size", `${size}px`);
    style.setProperty("--event-count-dot-gap", `${gap}px`);
    classList[stripe ? "add" : "remove"]("b-event-count-dots-striped");
    if (!this.isConfiguring) {
      this.refresh();
    }
  }
  updatemaxDots(maxDots) {
    VersionHelper.deprecate("calendar", "7.0.0", "maxDots property is deprecated in favor of eventDots");
    this.eventDots.max = maxDots;
    this.updateEventDots(this.eventDots);
  }
  refreshEventsMap() {
    var _a5, _b2;
    const me = this, eventSorter = me.eventSorter || ((_b2 = (_a5 = me.calendar) == null ? void 0 : _a5.activeView) == null ? void 0 : _b2.eventSorter);
    if (me.showEvents) {
      me.cellMap = me.eventStore[me.showEvents === "dots" ? "getEvents" : "getEventCounts"]({
        startDate: me.startDate,
        endDate: me.endDate,
        dateMap: me.cellMap || (me.cellMap = /* @__PURE__ */ new Map()),
        filter: me.eventFilter
      });
      if (me.showEvents === "dots" && eventSorter) {
        for (const eventInfo of me.cellMap.values()) {
          eventInfo.sort(eventSorter);
        }
      }
    }
  }
  cellRenderer({ cell, date: date2 }) {
    var _a5, _b2;
    const me = this, { showEvents } = me, isCount = showEvents === "count", isDots = showEvents === "dots", eventInfo = (_b2 = (_a5 = me.cellMap) == null ? void 0 : _a5.get) == null ? void 0 : _b2.call(_a5, DateHelper.makeKey(date2)), count = isDots ? eventInfo == null ? void 0 : eventInfo.length : eventInfo, countCls = isCount ? "b-cell-events-badge" : isDots ? "b-cal-minimal-event-container" : "b-icon b-icon-circle";
    if (isDots) {
      cell.innerHTML = "";
      DomHelper.createElement({
        class: "b-date-cell-inner",
        text: date2.getDate(),
        parent: cell
      });
    }
    cell.parentNode.setAttribute("aria-label", `${DateHelper.format(date2, "dddd MMMM Do YYYY")}${showEvents ? `, ${me.L("L{ResourceInfoColumn.eventCountText}", count || 0)}` : ""}.`);
    delete cell.parentNode.dataset.btip;
    if (count) {
      if (!isCount && me.eventCountTip) {
        cell.parentNode.dataset.btip = me.L("L{ResourceInfoColumn.eventCountText}", count);
      }
      const cellContent = {
        dataset: {
          count
        },
        class: {
          [countCls]: showEvents !== "heatmap",
          [SchedulerDatePicker.getEventCountClass(count)]: 1
        },
        parent: cell,
        [isCount ? "text" : ""]: count
      };
      if (isDots) {
        const { max: max3 } = me.eventDots, children = cellContent.children = [];
        for (let i = 0; i < Math.min((eventInfo == null ? void 0 : eventInfo.length) || 0, max3); i++) {
          const eventDomConfig = me.createEventDomConfig({
            date: date2,
            eventRecord: eventInfo[i],
            // If we only show one dot, we don't want to show whatever
            // the event colour is, we want to show the default dot colour.
            eventColor: max3 > 1 ? null : emptyString4,
            // renders without content with just background colour
            minimal: true
          });
          children.push(eventDomConfig);
        }
      }
      DomHelper.createElement(cellContent);
    }
  }
  static setupClass(meta) {
    meta.replaceType = true;
    super.setupClass(meta);
  }
};
__publicField(CalendarDatePicker, "$name", "CalendarDatePicker");
// Factoryable type name
__publicField(CalendarDatePicker, "type", "datepicker");
__publicField(CalendarDatePicker, "configurable", {
  /**
   * How to show presence of events in the configured {@link #config-eventStore} in the
   * day cells. Values may be:
   *
   * * `false` - Do not show events in cells.
   * * `true` - Show a themeable bullet to indicate the presence of events for a date.
   * * `'count'` - Show a themeable badge containing the event count for a date.
   * * `'dots'` - Show small event-coloured bullets up to a maximum of `{@link #config-eventDots}.max`
   *   to indicate event presence.
   * * `'heatmap'` - show warmer background colors the more events are present for a date
   * @prp {Boolean|'count'|'dots'|'heatmap'}
   * @default false
   */
  showEvents: null,
  /**
   * When {@link #config-showEvents} is `'dots'`, this is the maximum number of dots to show
   * below the date number in the cell.
   *
   * The default is four. Note that increasing this may lead to a cluttered UI in which the dots
   * obscure the date figure in the cell.
   * @deprecated 6.1.2 Use {@link #property-eventDots} instead.
   *
   * @prp {Number}
   * @default 4
   */
  maxDots: null,
  /**
   * When {@link #config-showEvents} is `'dots'`, this controls the layout of the dots
   * below the day number.
   *
   * If the `max` property is 1, the dots will be coloured using the default event colour,
   * *not* the colour of the event. This is to create a consistent appearance.
   *
   * @prp {Object}
   * @property {Number} marginTop The margin above the dots. Default is 4.
   * @property {Number} max The maximum number of dots to show. Default is 4.
   * @property {Number} gap The gap between dots. Default is 3.
   * @property {Number} size The size of the dots. Default is 5.
   * @property {Boolean} stripe If `true`, the dots will be striped, meaning they will be
   * joined together with _no gap_ between them and rounded at each end. Default is `false`.
   */
  eventDots: {
    marginTop: 4,
    max: 4,
    gap: 3,
    size: 5
  },
  /**
   * When {@link #config-showEvents} is __not__ `'count'`, setting this to
   * `true` means that a tooltip containing the event count will be shown on hover of
   * a day cell.
   *
   * @prp {Boolean}
   * @default
   */
  eventCountTip: null,
  alwaysRefreshOnMonthChange: true
});
CalendarDatePicker.initClass();
CalendarDatePicker._$name = "CalendarDatePicker";

// lib/Calendar/widget/Sidebar.js
var _Sidebar = class _Sidebar extends Panel.mixin(Responsive_default) {
  setupWidgetConfig(widgetConfig, type) {
    var _a5;
    const me = this;
    let result = super.setupWidgetConfig(...arguments);
    if (result.ref === "eventFilter") {
      result.store = me.initialConfig.eventStore;
    }
    if (result.ref === "resourceFilter") {
      if (!result.type || result.type === me.resourceFilter.type) {
        result = _Sidebar.mergeConfigs(me.resourceFilter, result);
      }
    }
    if (result.type === "datepicker") {
      me.element.classList.add("b-has-datepicker");
      me.element.classList.toggle("b-datepicker-with-events", Boolean(result.events));
      (_a5 = me.up("calendar")) == null ? void 0 : _a5.ion({
        rangeChange: "onCalendarViewRangeChange",
        thisObj: me
      });
    }
    return result;
  }
  // Process initial multiselection of DatePicker
  onInternalPaint({ firstPaint }) {
    var _a5, _b2;
    const { datePicker } = this.widgetMap;
    if (firstPaint) {
      this.getConfig("highlightMultiSelectSpan");
      if (datePicker == null ? void 0 : datePicker.multiSelect) {
        (_a5 = this.owner) == null ? void 0 : _a5.eachView((view) => {
          if (view.isDateRangeOwner && !view.fixedDuration) {
            if (datePicker.multiSelect === true) {
              view.dateFilter = view.resourceDateFilter = ({ date: date2 }) => datePicker.isSelected(date2);
            }
          }
        });
        this.onDatePickerSelectionChange({ source: datePicker, selection: datePicker.selection }, true);
      }
    }
    (_b2 = super.onInternalPaint) == null ? void 0 : _b2.call(this, ...arguments);
  }
  updateHighlightMultiSelectSpan(highlightMultiSelectSpan) {
    const { datePicker } = this.widgetMap;
    datePicker && datePicker.element.classList.toggle("b-highlight-selection", Boolean(highlightMultiSelectSpan));
  }
  updateSide(side, oldSide) {
    const { classList } = this.element;
    classList.remove(`b-sidebar-${oldSide}`);
    classList.add(`b-sidebar-${side}`);
    if (this.collapsible) {
      this.collapsible.direction = side;
    }
  }
  updateStepUnitText(stepUnitText) {
    const me = this, { prevButtonSidebar, nextButtonSidebar } = me.widgetMap;
    if (prevButtonSidebar) {
      prevButtonSidebar.tooltip = stepUnitText ? me.L("L{Calendar.previous}", stepUnitText) : "";
      prevButtonSidebar.disabled = !stepUnitText;
    }
    if (nextButtonSidebar) {
      nextButtonSidebar.tooltip = stepUnitText ? me.L("L{Calendar.next}", stepUnitText) : "";
      nextButtonSidebar.disabled = !stepUnitText;
    }
  }
  updateEventStore(eventStore) {
    const { resourceFilter, datePicker, eventFilter } = this.widgetMap;
    if (eventFilter) {
      eventFilter.store = eventStore;
    }
    if (resourceFilter) {
      resourceFilter.eventStore = eventStore;
    }
    if (datePicker) {
      datePicker.eventStore = eventStore;
    }
  }
  // Called when a DateRangeOwner mode changes its range. The event is relayed by the Calendar.
  // If the mode is an EventList in startDate->endDate mode, the date picker should match it
  onCalendarViewRangeChange({ source, new: { startDate, endDate } }) {
    const { datePicker } = this.widgetMap;
    if (source.isEventList && !source.range && (datePicker == null ? void 0 : datePicker.multiSelect) === "range") {
      datePicker.selection = [startDate, endDate];
    }
  }
  onDatePickerSelectionChange({ source, selection, oldSelection, userAction }, initialSelection) {
    var _a5;
    const me = this, { multiSelect } = source;
    const [startDate, endDate] = [selection[0], selection[selection.length - 1]];
    me.UIdateChange = userAction;
    if (startDate) {
      const [min4, max3] = [
        new Date(source.month.year, source.month.month, 1),
        new Date(source.month.year, source.month.month + 1, 0)
      ], visibleStartDate = multiSelect ? DateHelper.max(DateHelper.min(startDate, max3), min4) : startDate, viewConfig = {};
      if (multiSelect) {
        (_a5 = me.owner) == null ? void 0 : _a5.eachView((view) => {
          if (view.isDateRangeOwner && !view.fixedDuration) {
            const { _hidden } = view, range = source.getRange(multiSelect === true);
            if (view.hasConfig("endDate")) {
              viewConfig.startDate = range.startDate;
              viewConfig.endDate = DateHelper.add(range.endDate, view.isEventList ? 0 : -1, "d");
            } else {
              viewConfig.range = range;
            }
            if (!view.initialConfig.shiftIncrement && me.snapMultiSelectNavigation) {
              const snappedRange = multiSelect === true ? range : source.getRange(true);
              viewConfig.shiftIncrement = { unit: snappedRange.unit, magnitude: 1 };
            }
            if (!initialSelection && !DateHelper.betweenLesser(view.date, startDate, endDate)) {
              viewConfig.date = visibleStartDate;
            }
            view._hidden = true;
            view.setConfig(viewConfig);
            view._hidden = _hidden;
          }
        });
        if (!me.isConfiguring) {
          me.owner.eventStore.trigger("change");
        }
      }
      me.inOnDatePickerSelectionChange = true;
      me.date = visibleStartDate;
      me.inOnDatePickerSelectionChange = false;
      me.UIdateChange = false;
    }
  }
  changeDate(date2, oldDate) {
    date2 = typeof date2 === "string" ? DateHelper.parse(date2) : new Date(date2);
    if (isNaN(date2)) {
      throw new Error("Calendar widget date ingestion must be passed a Date, or a YYYY-MM-DD date string");
    }
    date2 = DateHelper.clearTime(date2);
    if (!oldDate || date2 - oldDate) {
      return date2;
    }
  }
  updateDate(date2, oldDate) {
    const me = this, { datePicker } = me.widgetMap;
    if (!me.isConfiguring && datePicker) {
      const { multiSelect, selection } = datePicker, clientView = me.owner.views.find((v) => v.isDateRangeOwner && !v.fixedDuration && v.isVisible);
      if (multiSelect && clientView) {
        const range = datePicker.getRange(multiSelect === true), config = {
          date: date2
        };
        let nextSelection;
        if (!me.inOnDatePickerSelectionChange) {
          if (multiSelect === true) {
            const forwards = date2 > oldDate, shiftIncrement = { unit: "day", magnitude: Math.abs(DateHelper.diff(range.startDate, clientView.startDate, "d")) };
            nextSelection = selection.map((d) => DateHelper.add(d, shiftIncrement.magnitude * (forwards ? 1 : -1), shiftIncrement.unit));
            if (range.wholeMonth) {
              const endDate = nextSelection[nextSelection.length - 1];
              let delta = DateHelper.getLastDateOfMonth(nextSelection[0]).getDate() - nextSelection.length;
              if (delta < 0) {
                nextSelection.length += delta;
              } else if (delta) {
                for (let date3 = DateHelper.add(endDate, 1, "d"); delta; delta--, date3 = DateHelper.add(date3, 1, "d")) {
                  nextSelection.push(date3);
                }
              }
            }
          } else {
            if (clientView.range) {
              date2 = clientView.snapDate(date2);
              nextSelection = [date2, DateHelper.add(DateHelper.add(date2, range), -1, "d")];
            } else {
              if (date2 > selection[selection.length - 1]) {
                range.magnitude *= -1;
                nextSelection = [DateHelper.add(DateHelper.add(date2, range), 1, "d"), date2];
              } else if (date2 < selection[0]) {
                nextSelection = [date2, DateHelper.add(DateHelper.add(date2, range), -1, "d")];
              }
            }
          }
          if (nextSelection) {
            config.selection = nextSelection;
          }
        }
        datePicker.setConfig(config);
      } else {
        datePicker.value = date2;
      }
    }
    me.trigger("dateChange", {
      date: date2,
      oldDate,
      userAction: Boolean(me.UIdateChange)
    });
  }
};
__publicField(_Sidebar, "$name", "Sidebar");
// Factoryable type name
__publicField(_Sidebar, "type", "sidebar");
__publicField(_Sidebar, "configurable", {
  focusable: false,
  ignoreParentReadOnly: true,
  ariaLabel: "Sidebar",
  bbar: {
    cls: "b-cal-nav-item",
    layout: {
      justify: "center"
    },
    items: {
      todayButtonSidebar: {
        text: "L{Calendar.Today}",
        onClick: "up.shiftToNow",
        weight: 200
      },
      prevButtonSidebar: {
        onClick: "up.shiftPrevious",
        cls: "b-borderless",
        icon: "b-icon-previous",
        weight: 300
      },
      nextButtonSidebar: {
        onClick: "up.shiftNext",
        cls: "b-borderless",
        icon: "b-icon-next",
        weight: 400
      }
    }
  },
  items: {
    datePicker: {
      type: "datepicker",
      ariaLabel: "L{Sidebar.selectDate}",
      weight: 100,
      internalListeners: {
        selectionChange: "up.onDatePickerSelectionChange"
      }
    },
    eventFilter: {
      type: "filterfield",
      field: "name",
      // We want this in second position
      weight: 150,
      placeholder: "L{Sidebar.matchEvents}",
      ariaLabel: "L{Sidebar.filterEvents}",
      localeClass: _Sidebar
    },
    resourceFilter: {
      ignoreParentReadOnly: true,
      ariaLabel: "L{Sidebar.filterResources}"
    }
  },
  date: {
    $config: {
      equal: "date"
    },
    value: null
  },
  eventStore: null,
  responsive: {},
  // brand us as responsive so "collapsed" is tracked per responsive state
  stepUnitText: null,
  /**
   * May be configured with `'left'` or `'right'` to dock the Sidebar to either side.
   *
   * Defaults to `'left'`.
   * @config {'left'|'right'}
   * @default
   */
  side: "left",
  collapsible: {
    tool: null
  },
  /**
   * The resource filtering widget which hides and shows events based upon
   * whether they are assigned to the selected resources.
   * @member {Scheduler.widget.ResourceFilter} resourceFilter
   */
  /**
   * A configuration object specifying options to change how the sidebar's resourcefilter is configured.
   * @config {ResourceFilterConfig|null}
   */
  resourceFilter: {
    type: "resourcefilter",
    flex: "1 1 auto",
    weight: 200,
    minHeight: 110,
    scrollable: {
      overflowY: "auto"
    },
    store: {
      sorters: [{
        field: "name",
        ascending: true
      }]
    },
    // We must only see the resources that are filtered in
    masterFilter(r) {
      return this.eventStore.resourceStore.isAvailable(r);
    }
  },
  /**
   * If the {@link Calendar.view.Calendar#property-datePicker} is set to {@link Core.widget.DatePicker#config-multiSelect}, setting this
   * to `true` shows a background highlight effect on the week rows that your selected range encompasses to help
   * the end user visualze the time ranges being evaluated.
   * @prp {Boolean}
   */
  highlightMultiSelectSpan: {
    $config: "lazy",
    value: null
  },
  /**
   * If the {@link Calendar.view.Calendar#property-datePicker} is set to {@link Core.widget.DatePicker#config-multiSelect}, then
   * by default, cooperating views (See {@link Calendar.view.Calendar#config-datePicker}) will have their
   * navigation `shiftIncrement` set to navigate week-by-week when using the `Next` and `Previous` buttons.
   *
   * Configure this as `false` to have the views step forwards or backwards by exactly the length of the
   * selected range.
   * @prp {Boolean}
   */
  snapMultiSelectNavigation: true
});
var Sidebar = _Sidebar;
Sidebar.initClass();
Sidebar._$name = "Sidebar";

// lib/Calendar/mixin/CalendarStores.js
var CalendarStores_default = (Target) => {
  var _a5;
  return _a5 = class extends ProjectConsumer_default(Target || Base) {
    //region Default config
    // This is the static definition of the Stores we consume from the project, and
    // which we must provide *TO* the project if we or our CrudManager is configured
    // with them.
    // The property name is the store name, and within that there is the dataName which
    // is the property which provides static data definition. And there is a listeners
    // definition which specifies the listeners *on this object* for each store.
    //
    // To process incoming stores, implement an updateXxxxxStore method such
    // as `updateEventStore(eventStore)`.
    //
    // To process an incoming Project implement `updateProject`. __Note that
    // `super.updateProject(...arguments)` must be called first.__
    static get projectStores() {
      return {
        resourceStore: {
          dataName: "resources",
          // eslint-disable-next-line bryntum/no-listeners-in-lib
          listeners: {
            changePreCommit: "onCalendarStoreChange"
          }
        },
        eventStore: {
          dataName: "events",
          // eslint-disable-next-line bryntum/no-listeners-in-lib
          listeners: {
            changePreCommit: "onCalendarStoreChange"
          }
        },
        assignmentStore: {
          dataName: "assignments"
        },
        timeRangeStore: {
          dataName: "timeRanges",
          modelClass: TimeRangeModel2
        },
        resourceTimeRangeStore: {
          dataName: "resourceTimeRanges",
          modelClass: ResourceTimeRangeModel
        }
      };
    }
    /**
     * Inline events, will be loaded into the {@link #property-eventStore}.
     * @prp {Scheduler.model.EventModel[]|EventModelConfig[]} events
     * @category Data
     */
    /**
     * The {@link Scheduler.data.EventStore} or a reconfiguring object for a store which will hold
     * the events to be rendered into the Calendar.
     * @config {Scheduler.data.EventStore|EventStoreConfig} eventStore
     * @category Data
     */
    /**
     * The {@link Scheduler.data.EventStore} holding the events to be rendered into the Calendar.
     * @member {Scheduler.data.EventStore} eventStore
     * @readonly
     * @category Data
     */
    /**
     * Inline resources, will be loaded into the {@link #property-resourceStore}.
     * @prp {Scheduler.model.ResourceModel[]|ResourceModelConfig[]} resources
     * @category Data
     */
    /**
     * The {@link Scheduler.data.ResourceStore} or a reconfiguring object for a store which will hold the resources to be rendered into the Calendar.
     * @config {Scheduler.data.ResourceStore|ResourceStoreConfig} resourceStore
     * @category Data
     */
    /**
     * The {@link Scheduler.data.ResourceStore} holding the resources to be rendered into the Calendar.
     * @member {Scheduler.data.ResourceStore} resourceStore
     * @readonly
     * @category Data
     */
    /**
     * Inline assignments, will be loaded into the {@link #property-assignmentStore}.
     * @prp {Scheduler.model.AssignmentModel[]|AssignmentModelConfig[]} assignments
     * @category Data
     */
    /**
     * The {@link Scheduler.data.AssignmentStore} or a reconfiguring object for a store which will hold assignments linking resources to events.
     * @config {Scheduler.data.AssignmentStore|AssignmentStoreConfig} assignmentStore
     * @category Data
     */
    /**
     * The {@link Scheduler.data.AssignmentStore} holding assignments linking resources to events.
     * @member {Scheduler.data.AssignmentStore} assignmentStore
     * @readonly
     * @category Data
     */
    /**
     * Inline time ranges, will be loaded into the {@link #property-timeRangeStore}.
     *
     * Time ranges are rendered in Calendar {@link Calendar.widget.DayView} or
     * {@link Calendar.widget.WeekView} when the Calendar has the {@link Calendar.feature.TimeRanges}
     * feature enabled.
     *
     * Time ranges are rendered into a {@link Scheduler.view.Scheduler} subview when that subview
     * has the {@link Scheduler.feature.TimeRanges} feature enabled.
     *
     * @prp {Calendar.model.TimeRangeModel[]|TimeRangeModelConfig[]} timeRanges
     * @accepts {Calendar.model.TimeRangeModel[]|TimeRangeModelConfig[]}
     * @category Data
     */
    /**
     * The {@link Calendar.data.TimeRangeStore} or a reconfiguring object for a store which will hold time ranges.
     * These may be used by the {@link Calendar.feature.TimeRanges} feature.
     * @config {Calendar.data.TimeRangeStore|TimeRangeStoreConfig} timeRangeStore
     * @category Data
     */
    /**
     * The {@link Calendar.data.TimeRangeStore} holding time ranges.
     * These may be used by the {@link Calendar.feature.TimeRanges} feature.
     * @member {Calendar.data.TimeRangeStore} timeRangeStore
     * @readonly
     * @category Data
     */
    /**
     * Inline resource time ranges, will be loaded into {@link #property-resourceTimeRangeStore}.
     *
     * Resource time ranges are rendered in a Calendar {@link Calendar.widget.ResourceView} when the
     * Calendar has the {@link Calendar.feature.TimeRanges} feature enabled.
     *
     * Resource time ranges are rendered into a {@link Scheduler.view.Scheduler} subview when that subview
     * has the {@link Scheduler.feature.ResourceTimeRanges} feature enabled.
     *
     * @prp {Scheduler.model.ResourceTimeRangeModel[]|ResourceTimeRangeModelConfig[]} resourceTimeRanges
     * @accepts {Scheduler.model.ResourceTimeRangeModel[]|ResourceTimeRangeModelConfig[]}
     * @category Data
     */
    /**
     * The {@link Scheduler.data.ResourceTimeRangeStore} or a reconfiguring object for a store which will hold resource time ranges.
     * These may be used by the {@link Calendar.feature.TimeRanges} feature.
     * @config {Scheduler.data.ResourceTimeRangeStore|ResourceTimeRangeStoreConfig} resourceTimeRangeStore
     * @category Data
     */
    /**
     * The {@link Scheduler.data.ResourceTimeRangeStore} holding resource time ranges.
     * These may be used by the {@link Calendar.feature.TimeRanges} feature.
     * @member {Scheduler.data.ResourceTimeRangeStore} resourceTimeRangeStore
     * @readonly
     * @category Data
     */
    //endregion
    //region CrudManager
    changeCrudManager(crudManager) {
      var _a6;
      const me = this;
      if (crudManager && !crudManager.isCrudManager) {
        const type = crudManager.type || me.crudManagerClass;
        crudManager = type.new({
          scheduler: me
        }, crudManager);
      }
      (_a6 = me.bindCrudManager) == null ? void 0 : _a6.call(me, crudManager);
      me._crudManager = null;
      return crudManager;
    }
    //endregion
    updateTimeZone(timeZone) {
      this.project.timeZone = timeZone;
    }
    get timeZone() {
      return this.project.timeZone;
    }
    // When project changes time zone, change Calendar date
    onBeforeTimeZoneChange({ timeZone, oldTimeZone }) {
      if (this.isConfiguring) {
        return;
      }
      const me = this, target = me.isCalendar ? me.activeView : !me.calendar && me;
      if ((target == null ? void 0 : target.duration) === 1) {
        const currentEvents = target.events.filter((e) => e.startDate >= target.startDate && e.endDate && e.endDate <= target.endDate);
        if (currentEvents.length) {
          const eventsCenterMs = currentEvents.map((e) => (e.startDate.getTime() + e.endDate.getTime()) / 2), avgCenterDate = new Date(Math.floor(eventsCenterMs.reduce((sum, e) => sum + e, 0) / eventsCenterMs.length)), centerDateLocal = oldTimeZone != null ? TimeZoneHelper.fromTimeZone(avgCenterDate, oldTimeZone) : avgCenterDate, centerDateTZ = timeZone != null ? TimeZoneHelper.toTimeZone(centerDateLocal, timeZone) : centerDateLocal;
          if (centerDateTZ < target.startDate || centerDateTZ > target.endDate) {
            (target.calendar || target).date = centerDateTZ;
          }
        }
      }
    }
    // Need this to not run conversion in ProjectConsumer
    onTimeZoneChange() {
    }
    /**
     * The default Calendar to be used when creating new events which do not initially have a resource.
     *
     * This includes {@link Calendar.view.Calendar#config-autoCreate dblclick-created} events and
     * {@link Calendar.feature.CalendarDrag drag-created} events and
     * {@link Calendar.feature.ExternalEventSource dragged in} events.
     * @property {Scheduler.model.ResourceModel}
     */
    get defaultCalendar() {
      const { eventStore } = this, defaultCalendar = ObjectHelper.hasOwn(this, "_defaultCalendar") ? this._defaultCalendar : eventStore.defaultCalendarId;
      if (defaultCalendar !== null) {
        return defaultCalendar != void 0 ? defaultCalendar.isResourceModel ? defaultCalendar : eventStore.resourceStore.getById(defaultCalendar) : eventStore.resourceStore.first;
      }
    }
    get defaultCalendarId() {
      return this.eventStore.modelClass.asId(this.defaultCalendar);
    }
    //region Project
    updateProject(project, oldProject) {
      super.updateProject(project, oldProject);
      const me = this;
      me.getConfig("timeZone");
      me.detachListeners("CalendarStores");
      if (project) {
        project.ion({
          name: "CalendarStores",
          dataReady: "onCalendarStoreChange",
          refresh: "onProjectRefresh",
          thisObj: me
        });
        if (!me.isConfiguring) {
          me.eachView((view) => {
            view.project = project;
          });
          me.onCalendarStoreChange({ source: project });
        }
      }
    }
    onProjectRefresh({ source: project, isInitialCommit }) {
      if (isInitialCommit) {
        project.eventStore.invalidateDayIndices();
      }
    }
    updateEventStore(eventStore) {
      if (eventStore && !eventStore.isCalendarEventStore) {
        const storeOwner = this;
        Object.defineProperty(eventStore, "defaultCalendar", {
          get: () => storeOwner.defaultCalendar
        });
        eventStore.isCalendarEventStore = true;
        if (!("removeUnassignedEvent" in eventStore.initialConfig)) {
          eventStore.removeUnassignedEvent = false;
        }
      }
    }
    //endregion
    //region WidgetClass
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
    //endregion
  }, __publicField(_a5, "$name", "CalendarStores"), __publicField(_a5, "configurable", {
    /**
     * The calendar ({@link Scheduler.model.ResourceModel Resource}), (or its `id`) to use as the
     * default calendar for new events created using {@link Calendar.view.Calendar#config-autoCreate dblclick}
     * or {@link Calendar.feature.CalendarDrag drag-create}, or {@link Calendar.feature.ExternalEventSource dragging in}.
     *
     * May be specified as the `id` of an existing resource, or a resource record that is present in the project.
     *
     * If not specified, the first record in the {@link #property-resourceStore} will be used.
     *
     * If specified as `null`, then new events created using {@link Calendar.view.Calendar#config-autoCreate dblclick}
     * or {@link Calendar.feature.CalendarDrag drag-create}, or {@link Calendar.feature.ExternalEventSource dragging in}
     * will not be automatically assigned a calendar.
     * @config {String|Scheduler.model.ResourceModel}
     */
    defaultCalendar: null,
    /**
     * Class that should be used to instantiate a CrudManager in case it's provided as a simple object to
     * {@link #config-crudManager} config.
     * @config {Scheduler.data.CrudManager}
     * @category Data
     */
    crudManagerClass: CrudManager2,
    /**
     * Get/set the CrudManager instance
     * @member {Scheduler.data.CrudManager} crudManager
     * @category Data
     */
    /**
     * Supply a {@link Scheduler.data.CrudManager} instance or a config object if you want to use
     * CrudManager for handling data.
     * @config {CrudManagerConfig|Scheduler.data.CrudManager}
     * @category Data
     */
    crudManager: null,
    /**
     * Set to a time zone or a UTC offset. This will set the project's
     * {@link Scheduler.model.ProjectModel#config-timeZone} config accordingly. As this config is only a referer,
     * please see project's config {@link Scheduler.model.ProjectModel#config-timeZone documentation} for more
     * information.
     *
     * ```javascript
     * new Calendar(){
     *   timeZone : 'America/Chicago'
     * }
     * ```
     * @config {String|Number} timeZone
     * @category Misc
     */
    timeZone: {
      // Don't ingest the config eagerly because it relies on project being present.
      // Lazy means it waits for ingestion until timeZone property is referenced.
      $config: "lazy",
      value: null
    }
  }), __publicField(_a5, "defaultConfig", {
    projectModelClass: ProjectModel2
  }), _a5;
};

// lib/Calendar/mixin/SchedulerInterface.js
var hasEventStore = (w) => w.isCalendarMixin && w.eventStore;
var eventNameSplitter = /(click|touch|mouse|pointer|key|context|dbl)(.*)/;
var eventPartReplacer = (match, prefix, remainder) => `${StringHelper.capitalize(prefix)}${StringHelper.capitalize(remainder)}`;
var capitalizeEvent = (domEvent) => domEvent.type.replace(eventNameSplitter, eventPartReplacer);
var SchedulerInterface_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    /**
     * Determines what is under the cursor of the specified event or what is described by the given element.
     * @param {Event|Element} domEvent The event or element
     * @returns {CalendarHit}
     */
    calendarHitTest(domEvent) {
      const { activeView } = this, target = DomHelper.getEventElement(domEvent);
      return (activeView == null ? void 0 : activeView.element.contains(target)) ? activeView.calendarHitTest(domEvent) : null;
    }
    // Hack to expose part of the Scheduler API that features and mixins inherited from Scheduler use.
    get timeAxis() {
      const me = this;
      if (!me._timeAxis) {
        me._timeAxis = {
          get startDate() {
            return me.activeView.startDate;
          },
          get endDateDate() {
            return me.activeView.endDate;
          },
          isTimeSpanInAxis(event) {
            return DateHelper.intersectSpans(event.startDate, event.endDate, this.startDate, this.endDate);
          }
        };
      }
      return this._timeAxis;
    }
    // Hack to expose part of the Scheduler API that features use.
    get timeAxisViewModel() {
      const me = this;
      if (!me._timeAxisViewModel) {
        me._timeAxisViewModel = {
          get timeResolution() {
            return me.activeView.timeResolution || {};
          }
        };
      }
      return this._timeAxisViewModel;
    }
    /**
     * Interface method required for navigation/selection.
     * Returns the event record for a DOM element.
     * @param {HTMLElement|Event} elementOrEvent The DOM node to lookup, or a DOM event whose target to lookup.
     * @returns {Scheduler.model.EventModel} The event record
     */
    resolveEventRecord(elementOrEvent) {
      var _a6;
      if (elementOrEvent instanceof Event) {
        elementOrEvent = elementOrEvent.target;
      }
      const activeClient = (_a6 = this.constructor.fromElement(elementOrEvent)) == null ? void 0 : _a6.closest(hasEventStore);
      return activeClient == null ? void 0 : activeClient.getEventRecord(elementOrEvent);
    }
    /**
     * Interface method required for navigation/selection
     * Assignment not supported in Calendar.
     * @private
     */
    resolveAssignmentRecord() {
    }
    /**
     * Interface method required for navigation/selection.
     * Returns the resource (calendar) record for a DOM element.
     * @param {HTMLElement|Event} elementOrEvent The DOM node to lookup, or a DOM event whose target to lookup.
     * @returns {Scheduler.model.ResourceModel} Any corresponding event record's resource if any.
     */
    resolveResourceRecord(elementOrEvent) {
      var _a6, _b2;
      const element = elementOrEvent.nodeType === Node.ELEMENT_NODE ? elementOrEvent : elementOrEvent.target;
      return this.resourceStore.getById((_a6 = element.closest("[data-resource-id]")) == null ? void 0 : _a6.dataset.resourceId) || ((_b2 = this.resolveEventRecord(element)) == null ? void 0 : _b2.resource);
    }
    handleEvent(domEvent) {
      var _a6;
      const eventName = capitalizeEvent(domEvent);
      if (super.handleEvent) {
        super.handleEvent(domEvent);
      } else if (!domEvent.handled && !domEvent.schedulerRedirected) {
        if (this.trigger(`beforeElement${eventName}`, { event: domEvent, target: domEvent.target }) !== false) {
          (_a6 = this[`onElement${eventName}`]) == null ? void 0 : _a6.call(this, domEvent);
        }
        domEvent.schedulerRedirected = true;
      }
    }
    getCellDataFromEvent(domEvent) {
      var _a6;
      return (_a6 = super.getCellDataFromEvent) == null ? void 0 : _a6.call(this, domEvent);
    }
    getDateFromDomEvent(domEvent) {
      var _a6;
      const activeClient = (_a6 = this.constructor.fromElement(domEvent)) == null ? void 0 : _a6.closest(hasEventStore);
      if (activeClient) {
        return activeClient.getDateFromDomEvent(domEvent);
      }
    }
    getDateFromXY(xy2, roundingMethod, local = true, allowOutOfRange) {
      const activeClient = this.activeSubView;
      if (activeClient) {
        return activeClient.getDateFromPosition(xy2[0] - globalThis.scrollX, xy2[1] - globalThis.scrollY, local);
      }
    }
    // Used by shared feature code.
    // Route to view's create implementation.
    createEvent() {
      this.activeView.createEvent(...arguments);
    }
    // It's fixed to be enabled for now
    get enableRecurringEvents() {
      return true;
    }
    intersectsVisibleTimeSpan(timespan) {
      const { activeView } = this;
      return DateHelper.intersectSpans(activeView.startDate, activeView.endDate, timespan.startDate, timespan.endDate);
    }
    // Used by shared feature code. Not relevant in Calendar
    getRowFor() {
    }
    // Used by shared feature code. Could implement if needed.
    suspendRefresh() {
    }
    // Used by shared feature code. Could implement if needed.
    resumeRefresh() {
    }
    get widgetClass() {
    }
  }, __publicField(_a5, "$name", "SchedulerInterface"), _a5;
};

// lib/Calendar/localization/En.js
var locale5 = {
  localeName: "En",
  localeDesc: "English (US)",
  localeCode: "en-US",
  localeRtl: false,
  EventEdit: {
    Calendar: "Calendar",
    "All day": "All day",
    day: "Day",
    week: "Week",
    month: "Month",
    year: "Year",
    decade: "Decade"
  },
  EventMenu: {
    duplicateEvent: "Duplicate",
    copy: "copy"
  },
  Calendar: {
    toggleSidebar: "Toggle sidebar visibility",
    Today: "Today",
    Tomorrow: "Tomorrow",
    next: (range) => `Next ${range}`,
    previous: (range) => `Previous ${range}`,
    plusMore: (value2) => `+${value2} more`,
    moreEvents: (value2) => `${value2} more events`,
    allDay: "All day",
    endsOn: (d) => `Ends ${d}`,
    weekOfYear: ([y, w]) => `Week ${w}, ${y}`,
    loadFail: "Calendar data load failed. Please contact your system administrator",
    gotoDate: "Go to date",
    timeRange: "Time range"
  },
  CalendarDrag: {
    holdCtrlForRecurrence: "Hold CTRL for a recurring event"
  },
  CalendarMixin: {
    eventCount: (count) => `${count || "No"} event${count === 1 ? "" : "s"}`
  },
  EventTip: {
    "Edit event": "Edit",
    timeFormat: "LST"
  },
  ModeSelector: {
    includeWeekends: "Include weekends",
    weekends: "Weekends"
  },
  AgendaView: {
    Agenda: "Agenda"
  },
  MonthView: {
    Month: "Month",
    monthUnit: "month"
  },
  WeekView: {
    weekUnit: "week"
  },
  YearView: {
    Year: "Year",
    yearUnit: "year",
    noEvents: "No events"
  },
  EventList: {
    List: "List",
    Start: "Start",
    Finish: "End",
    days: (count) => `${count > 1 ? `${count} ` : ""}day${count === 1 ? "" : "s"}`
  },
  DayView: {
    Day: "Day",
    dayUnit: "day",
    daysUnit: "days",
    expandAllDayRow: "Expand all-day section",
    collapseAllDayRow: "Collapse all-day section",
    timeFormat: "LST",
    timeAxisTimeFormat: "LST",
    noMatchingDates: "No matching dates"
  },
  DayAgendaView: {
    dayAgenda: "Day Agenda"
  },
  DayResourceView: {
    dayResourceView: "Day resources"
  },
  Sidebar: {
    selectDate: "Select calendar date",
    matchEvents: "Match events",
    filterEvents: "Filter events by event name",
    filterResources: "Filter events by selected resource"
  },
  WeekExpander: {
    expandTip: "Click to expand row",
    collapseTip: "Click to collapse row"
  }
};
var En_default4 = LocaleHelper.publishLocale(locale5);

// lib/Calendar/util/EventSorter.js
var EventSorter = class extends Base {
  /**
   * This sorting method implement following rules:
   *
   *  0. Unscheduled events go before scheduled ones
   *  1. Compare by floored start date
   *  2. If equal, compare by floored duration
   *  3. If equal, compare by start date
   *  4. If equal, compare by duration
   * @param {Scheduler.model.EventModel|EventBar} event1
   * @param {Scheduler.model.EventModel|EventBar} event2
   * @returns {Number}
   */
  static defaultSorterFn(event1, event2) {
    event1 = event1.eventRecord || event1;
    event2 = event2.eventRecord || event2;
    const { startDate: start1 } = event1, { startDate: start2 } = event2;
    if (!start1) {
      return -1;
    }
    if (!start2) {
      return 1;
    }
    return start1 - start2 || event2.durationMS - event1.durationMS;
  }
  /**
   * This sorting method implement following rules:
   *
   *  0. Unscheduled events go before scheduled ones
   *  1. An {@link Scheduler.model.EventModel#property-isInterDay} event sorts before events that are not.
   *  2. Compare floored start date, if the events have different values
   *  3. Compare floored duration, if the events have different values
   *  4. Compare start date, if the events have different values
   *  5. Compare duration
   *
   * This is used by {@link Calendar.widget.MonthView} since this view mixes
   * both interday and intraday events in the same view but render them differently.
   * @param {Scheduler.model.EventModel|EventBar} event1
   * @param {Scheduler.model.EventModel|EventBar} event2
   * @returns {Number}
   * @internal
   */
  static interDaySorterFn(event1, event2) {
    event1 = event1.eventRecord || event1;
    event2 = event2.eventRecord || event2;
    const {
      startDate: start1,
      isInterDay: event1InterDay
    } = event1, {
      startDate: start2,
      isInterDay: event2InterDay
    } = event2;
    if (!start1) {
      return -1;
    }
    if (!start2) {
      return 1;
    }
    if (event1InterDay !== event2InterDay) {
      return Number(event2InterDay) - Number(event1InterDay);
    }
    if (!event1InterDay && !event2InterDay) {
      return start1 - start2;
    }
    return event2.durationMS - event1.durationMS || event2.endingDate - event1.endingDate || start1 - start2;
  }
};
EventSorter._$name = "EventSorter";

// lib/Calendar/widget/mixin/CalendarMixin.js
var emptyObject16 = Object.freeze({});
var { eventNameMap: eventNameMap3 } = EventHelper;
var isFocusedCalendarMixin = (w) => w.isCalendarMixin && w.containsFocus;
var CalendarMixin_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base).mixin(
    Describable_default,
    SchedulerInterface_default,
    Featureable_default,
    CalendarStores_default,
    EventRenderer_default,
    Responsive_default
  ) {
    static get configurable() {
      return {
        //region Hidden configs
        /**
         * @hideevents eventSelectionChange
         */
        /**
         * @hideconfigs htmlCls, autoUpdateRecord, record, textContent, content, html
         */
        /**
         * @hideproperties  content, html
         */
        //endregion
        /**
         * A String which describes how much the {@link #function-next} and {@link #function-previous}
         * methods will move this view forwards or backwards in time.
         *
         * This is used to create the tooltip hints for the `nextButton` and `prevButton` in the
         * {@link Calendar.view.Calendar#property-tbar Calendar's toolbar}. If this property
         * is not defined, the `nextButton` and `prevButton` will be disabled when this view is active.
         *
         * Note that {@link Calendar.widget.WeekView} and {@link Calendar.widget.YearView} use a localized
         * string property to yield this value. Other view types implement a `get stepUnit` getter because
         * their step increments are variable.
         * @member {String} stepUnit
         * @readonly
         */
        /**
         * A function that provides the textual description for this view. If provided, this function overrides the
         * {@link #config-descriptionFormat}.
         *
         * ```javascript
         *  descriptionRenderer() {
         *      const
         *          {
         *              startDate,
         *              endDate
         *          }         = this,
         *          sameMonth = startDate.getMonth() === endDate.getMonth(),
         *          sd        = DateHelper.format(startDate, sameMonth ? 'DD{->}' : 'DD/MM/YYY{ -> }'),
         *          ed        = DateHelper.format(endDate, 'DD/MM/YYY');
         *
         *     return `${sd}${ed}, ${this.L('L{eventCount}', this.eventCount)}`
         * }
         * ```
         * @config {Function} descriptionRenderer
         * @param {CalendarView} view The active view in case the function is in another scope.
         * @returns {String} Description string
         */
        /**
         * Configure this as `false` to disable drag-creating events in this view.
         * @prp {Boolean} allowDragCreate
         */
        /**
         * Configure this as `false` to disable drag-moving events in this view.
         * @prp {Boolean} allowDragMove
         */
        /**
         * Configure this as `false` to disable drag-resizing events in this view.
         * @prp {Boolean} allowDragResize
         */
        localizableProperties: [
          "autoCreate.newName",
          "timeFormat",
          "shortDateFormat",
          "shortDateTimeFormat"
        ],
        // Calendar view layouts must not be changed by attribute mutation
        contentElMutationObserver: null,
        eventStore: null,
        resourceStore: null,
        /**
         * A function which compares events which some views use to decide upon rendering order.
         *
         * Default sorter function are provided from the {@link Calendar.util.EventSorter} class.
         *
         * A custom sort function may be configured.
         *
         * Note that the two objects to compare may either be {@link Scheduler.model.EventModel}s
         * or {@link EventBar}s which contain an `eventRecord` property which is the {@link Scheduler.model.EventModel}.
         * @config {Function}
         * @param {Scheduler.model.EventModel|EventBar} lhs The left side value to conpare
         * @param {Scheduler.model.EventModel|EventBar} rhs The right side value to conpare
         * @returns {Number}
         */
        eventSorter: EventSorter.defaultSorterFn,
        responsive: {},
        // brand as responsive so b-responsive-xxx CSS classes get added
        /**
         * Configure as `true` to hide {@link #config-nonWorkingDays}
         * @prp {Boolean}
         */
        hideNonWorkingDays: null,
        hideNonWorkingDaysCls: "b-hide-nonworking-days",
        /**
         * The week start day, 0 meaning Sunday, 6 meaning Saturday.
         * Defaults to {@link Core.helper.DateHelper#property-weekStartDay-static}.
         * @config {Number}
         */
        weekStartDay: DateHelper.weekStartDay,
        /**
         * Non-working days as an object where keys are day indices, 0-6 (Sunday-Saturday), and the value is `true`.
         * Defaults to {@link Core.helper.DateHelper#property-nonWorkingDays-static}.
         * @config {Object<Number,Boolean>}
         */
        nonWorkingDays: {
          value: DateHelper.nonWorkingDays,
          $config: {
            merge: "replace"
          }
        },
        /**
         * The class name to add to calendar cells which are non working days.
         * @config {String}
         * @private
         */
        nonWorkingDayCls: "b-nonworking-day",
        /**
         * The class name to add to calendar cells.
         * @member {String} dayCellCls
         */
        /**
         * The class name to add to calendar cells.
         * @config {String}
         * @private
         */
        dayCellCls: "b-calendar-cell",
        /**
         * The class name to add to calendar cells which are weekend days.
         * @config {String}
         * @private
         */
        weekendCls: "b-weekend",
        todayCls: "b-today",
        /**
         * The class name to add to events which have an end date in the past. Defaults to `'b-past-event'`.
         *
         * There are no CSS rules attached to this class. You may use it to style past events.
         * @prp {String}
         * @default
         */
        pastEventCls: "b-past-event",
        /**
         * The class name to add to events which have duration less than or equal to
         * {@link #config-shortEventDuration}.
         * @config {String}
         */
        shortEventCls: "b-short-event",
        /**
         * The duration at which below and equal to this value, an event's encapsulating element gets
         * the {@link #config-shortEventCls} added to it so that small event bars can have style rearrangements.
         *
         * In {@link Calendar.widget.DayView}s, short events have compressed layout so that the event name is
         * visible on the top line next to the start time.
         *
         * This may be a string in the format required by {@link Core.helper.DateHelper#function-parseDuration-static}.
         *
         * It may also be configured as a millisecond value.
         * @config {String|Number}
         * @default
         */
        shortEventDuration: "30 minutes",
        /**
         * The height of event bars if this view creates event bars.
         *
         * {@link Calendar.widget.MonthView MonthView}, {@link Calendar.widget.MonthView CalendarRow}
         * (the {@link Calendar.widget.DayView#config-allDayEvents all day row} in a
         * {@link Calendar.widget.WeekView WeekView}) and {@link Calendar.widget.AgendaView AgendaView}
         * use this config.
         *
         * In {@link Calendar.widget.DayView DayView} and {@link Calendar.widget.WeekView WeekView},
         * the event element's height is part of the widget's layout and signifies the event's duration,
         * so these use a default value of `'auto'`.
         * @config {Number|String}
         * @default
         */
        eventHeight: 25,
        eventSpacing: 2,
        intradayCls: "b-intraday",
        alldayCls: "b-allday",
        solidBarCls: "b-solid-bar",
        dayNameSelector: ".b-day-name",
        // MonthView widens this to be the whole cell header
        // if it's showing a separate week number column.
        // CalendarRow always widens this to the cell header.
        showTime: false,
        /**
         * A {@link Core.helper.DateHelper} format string used to format the time displayed in events
         *
         * @config {String}
         * @default 'LT'
         */
        timeFormat: {
          value: "LT",
          $config: {
            localeKey: "L{timeFormat}"
          }
        },
        /**
         * __Not applicable in a `DayView`__
         *
         * This specifies whether to show a circular "bullet" icon if there is no
         * {@link Scheduler.model.EventModel#field-iconCls} defined for an event.
         *
         * By default, events which are rendered as solid blocks of colour (such as all day events) do __not__
         * show the bullet icon.
         *
         * By default, events which do not show as a colour block show the bullet icon as a means of showing
         * the event's defined `eventColor`.
         *
         * This property may contain two properties which define whether to show the bullet icon for both
         * event types.
         *
         * If configured as `true`, all event bars will show a bullet icon if they do not have an
         * {@link Scheduler.model.EventModel#field-iconCls}
         * @config {Boolean|Object}
         * @param {Boolean} [bar] This is `false` by default. Set this to `true` in modes where a solid event
         * bar should show a bullet icon
         * @param {Boolean} [noBar] This is `true` by default. Events with no background colour, use this to
         * show the event's defined `eventColor`
         * @default
         */
        showBullet: {
          bar: false,
          noBar: true
        },
        eventColourStyleProperty: "color",
        // By default the event color becomes the colour
        // DayView uses backgroundColor
        handlePointerInteraction: true,
        /**
         * A {@link Core.helper.DateHelper} format string to use to create date output for
         * abbreviated view descriptions.
         * @prp {String}
         * @default 'll'
         */
        shortDateFormat: {
          value: "ll",
          $config: {
            localeKey: "L{shortDateFormat}"
          }
        },
        /**
         * A title text used by the Calendar's mode selector button. If not specified, the mode name is used.
         * @config {String}
         */
        title: null,
        /**
         * A {@link Core.helper.DateHelper} format string to use to create date and time output for
         * abbreviated view descriptions.
         * @prp {String}
         * @default 'll LT'
         */
        shortDateTimeFormat: {
          value: "ll LT",
          $config: {
            localeKey: "L{shortDateTimeFormat}"
          }
        },
        /**
         * Configure as `true` to make the view read-only, by disabling any UIs for modifying data.
         *
         * __Note that checks MUST always also be applied at the server side.__
         * @config {Boolean}
         */
        readOnly: null,
        /**
         * If this config is set, then the `gesture` configured (which defaults to `dblclick`) creates a
         * new event at the mouse or touch event's time point.
         *
         * The exact time is rounded to the closest specified `step` value. This value is also used
         * for the {@link Core.widget.TimeField#property-step} value in the {@link Calendar.feature.EventEdit}'s
         * time input field.
         *
         * The duration of the created event is the specified `duration` value.
         *
         * If this is specified as `true`, the `gesture` becomes `dblclick`, and the other properties
         * are the default values listed below.
         *
         * If this is specified as a string, the string becomes the `gesture`, and the other properties
         * are the default values listed below.
         *
         * @prp {Boolean|String|Object}
         * @property {String} [gesture='dblclick'] The DOM event name which initiates event creation at the event's position.
         * @property {Function|String} [newName='New Event'] The name of an event created using `autoCreate` or a function to call which yields the name.
         * When a function is specified, the following parameters are passed:
         * - <span class="name">view : <span class="type">CalendarMixin</span></span><div class="description">The view in which the event is being created.</div>
         * - <span class="name">startDate : <span class="type">String</span></span><div class="description">The start date of the event being created.</div>
         * - <span class="name">resourceRecord : <span class="type">ResourceModel</span></span><div class="description">The resource of the event being created.</div>
         * @property {String} [step='15 minutes'] The time unit by which to snap the start click point of auto created events.
         * __Only for views which have a granularity of less than one day such as `WeekView` and `DayView`__.
         *
         * For views which show whole days, the start defaults to 8am.
         *
         * This is a string in the format required by {@link Core.helper.DateHelper#function-parseDuration-static}.
         *
         * This value is also used for the {@link Core.widget.TimeField#property-step} value in the
         * {@link Calendar.feature.EventEdit}'s time input field.
         *
         * @property {'round'|'ceil'|'floor'} [snapType='round'] How to snap a precise gesture time to a boundary specified by the `step` property.
         * __Only for views which have a granularity of less than one day such as `WeekView` and `DayView`__.
         * @property {String} [duration='1 hour'] The default start hour for auto created events in the form accepted by {@link Core.helper.DateHelper#function-parseDuration-static}
         * @property {Number} [startHour=8] The default start hour for auto created events
         * in views where the time granularity is one day. In a `DayView` or `WeekView` where a mouse event position
         * will translate to a time of day, this is not used.
         *
         * This is the hour of the day to start the event at. It may be fractional.
         * @accepts {Object|String|Boolean}
         * @default
         */
        autoCreate: {
          gesture: "dblclick",
          newName: "L{Object.newEvent}",
          step: "15 minutes",
          snapType: "round",
          duration: "1 hour",
          startHour: 8
        },
        /**
         * The {@link Scheduler.model.EventModel#field-durationUnit} to use when drag-creating events
         * in this view.
         *
         * For {@link Calendar.widget.DayView}s, this is normally `'hour'`, for views with a granularity
         * level of one day, the default is `'day'`.
         *
         * Configure this as `null` to have drag-create use the unit of the `duration` property of
         * the {@link #config-autoCreate} config as the duration unit when drag-creating events.
         * @prp {String}
         */
        dragUnit: "hour",
        autoRefresh: {
          $config: {
            merge: "classList"
          },
          value: null
        },
        /**
         * Set to `false` if you don't want to allow events overlapping times for any one resource (defaults to true).
         * @config {Boolean}
         * @default
         * @private
         */
        allowOverlap: true,
        /**
         * The converse of {@link #property-syncCalendarDate}
         *
         * When used as a {@link Calendar.view.Calendar#config-modes mode} of a Calendar, this
         * view's date will automatically be kept synced with the Calendar's
         * {@link Calendar.view.Calendar#property-date}. So this view will move to encapsulate
         * the Calendar's date.
         *
         * Configure this as `false` to opt out of this.
         *
         * __Note that this places the onus on the application developer to control the
         * viewed date range in this widget.__
         * @prp {Boolean} syncViewDate
         * @default
         */
        syncViewDate: true,
        /**
         * The converse of {@link #property-syncViewDate}
         *
         * When a date, or an event is clicked in a calendar view, the Calendar's {@link Calendar.view.Calendar#property-date}
         * is set to the clicked date to indicate that the user has expressed interest in that date.
         *
         * This is for convenience, so that when switching modes, by default, the time span encapsulating
         * the last date of interest is always shown.
         *
         * To disable syncing the calendar's date with this view's date, configure this property as `false`.
         * @prp {Boolean} syncCalendarDate
         * @default
         */
        syncCalendarDate: true,
        // For views which are Panels, make them not include a tabIndex
        focusable: false,
        // Allow an AvatarRendering instance to be specified
        avatarRendering: {
          $config: "lazy",
          value: null
        },
        /**
         * Configure as `true` to show avatars of the assigned resources (calendars) at the
         * start of the event bar.
         *
         * Configure as `'last'` to show avatars of the assigned resources (calendars) at the
         * end of the event bar.
         *
         * Note that the avatars are `2.22em` diameter circles, and this may not be suitable
         * for rendering in short events inside a DayView.
         *
         * In a view which renders event bars, the {@link #config-eventHeight} should be
         * increased from the default to accommodate the extra information.
         *
         * Note that you must set {@link #config-resourceImagePath} in order that the system
         * knows where to access the resource's image file from.
         *
         * If no image is set, or the image is not found, the resource's initials are shown instead.
         *
         * By default it is inherited from the owning Calendar:
         * ```javascript
         * new Calendar({
         *     resourceImagePath   : 'images/resources/'
         *     modes : {
         *         month : {
         *             showResourceAvatars : true,
         *         },
         *         week : {
         *             // Images go at the end of the body with name first
         *             showResourceAvatars : 'last,
         *         }
         *     }
         * });
         * ```
         * @config {Boolean|String}
         * @default false
         */
        showResourceAvatars: null,
        /**
         * Path to load resource images from. Used by the {@link #config-showResourceAvatars} config
         * to create URLs using the resource's
         * {@link Scheduler/model/ResourceModel#field-image} or
         * {@link Scheduler/model/ResourceModel#field-imageUrl} fields:
         *
         * * `image` represents image name inside the specified `resourceImagePath`,
         * * `imageUrl` represents fully qualified image URL.
         *
         * **NOTE**: The path should end with a `/`:
         *
         * ```javascript
         * new Calendar({
         *     modeDefaults : {
         *         showResourceAvatars : true,
         *         resourceImagePath   : 'images/resources/'
         *     }
         * });
         * ```
         * @config {String}
         */
        resourceImagePath: null,
        /**
         * The minimum date to which the `startDate` of this view may be navigated.
         * @member {Date} minDate
         */
        /**
         * The minimum date to which the `startDate` of this view may be navigated.
         * @config {Date|String}
         */
        minDate: null,
        /**
         * The maximum date to which the `endDate` of this view may be navigated.
         * @member {Date} maxDate
         */
        /**
         * The maximum date to which the `endDate` of this view may be navigated.
         * @config {Date|String}
         */
        maxDate: null,
        /**
         * By default, when navigating through time, the next time
         * block will be animated in from the appropriate direction.
         *
         * Configure this as `false` to disable this.
         * @prp {Boolean} animateTimeShift
         * @default
         */
        animateTimeShift: true,
        // Private at this level, it's only processed for a ResourceView
        includeTimeRanges: null,
        /**
         * Options to add into scroll options when the {@link #function-scrollTo} method is called.
         *
         * By defult, scrolling is animated.
         *
         * To make the scrolling animate with easing and highlight the target element, configure the view with
         *
         * ```javascript
         * {
         *     defaultScrollOptions : {
         *         highlight : true,
         *         animate   : {
         *             duration : 750,
         *             easing   : 'bounce'
         *         }
         *     }
         * }
         * ```
         *
         * @prp {BryntumScrollOptions} defaultScrollOptions
         * @default
         */
        defaultScrollOptions: { animate: true },
        /**
         * A button configuration object to use for the automatically created mode selector button for this mode.
         * @config {ButtonConfig}
         */
        selectorButton: null,
        /**
         * A menu item configuration object to use for the automatically created mode selector menu item for this mode.
         * @config {MenuItemConfig}
         */
        selectorMenuItem: null,
        testConfig: {
          animateTimeShift: false
        },
        /**
         * By default, the end date of an all day event is displayed in the UI as
         * the last calendar date on which the event falls. For most end users, this is the
         * expected value.
         *
         * Technically, the {@link Scheduler.model.EventModel#field-endDate} is a timestamp
         * which represents the exact point in time at which an event ends. To use this instead,
         * configure `extendAllDayEndDay` as `true`.
         *
         * To be clear, this would mean that an {@link Scheduler.model.EventModel#field-allDay}
         * event starting and ending on the 7th of February 2020, would show the end date in the
         * tooltip as 8th of February 2020.
         * @prp {Boolean}
         * @default false
         */
        extendAllDayEndDay: null,
        /**
         * A key to use to activate this view.
         *
         * This is optional and defaults to the first letter of the {@link #config-title} property.
         *
         * The title defaults to the mode name, so by default, the activation key is the first letter of the mode name.
         * @config {String} [activationKey]
         */
        activationKey: null,
        keyMap: {
          "Ctrl+z": "undoRedoDataChange",
          "Shift+Ctrl+z": "undoRedoDataChange"
        }
      };
    }
    static get delayable() {
      return {
        refreshSoon: {
          type: "raf",
          cancelOutstanding: true
        }
      };
    }
    static get featureable() {
      return {
        factory: CalendarFeature
      };
    }
    construct(config) {
      var _a6;
      const me = this;
      super.construct(config);
      if (!me.isYearView && me.element.tabIndex !== -1 && ((_a6 = me.contentElement) == null ? void 0 : _a6.tabIndex) !== -1) {
        (me.contentElement || me.element).tabIndex = -1;
      }
      me.getConfig("avatarRendering");
      EventHelper.on({
        element: me.element,
        keydown: "onCalendarKeyDown",
        thisObj: me
      });
      if (me.handlePointerInteraction) {
        EventHelper.on({
          element: me.element,
          mouseover: "onEventMouseOverOut",
          mouseout: "onEventMouseOverOut",
          mousedown: "onCalendarPointerInteraction",
          mouseup: "onCalendarPointerInteraction",
          // Block subsequent clicks before 300ms has elapsed
          click: {
            handler: "onCalendarPointerInteraction",
            block: 300
          },
          dblclick: "onCalendarPointerInteraction",
          contextmenu: "onCalendarPointerInteraction",
          thisObj: me
        });
        EventHelper.on({
          element: me.element,
          mouseenter: "onEventMouseEnterLeave",
          mouseleave: "onEventMouseEnterLeave",
          capture: true,
          thisObj: me
        });
      }
    }
    /**
     * Focuses the cell which encapsulates the passed date if possible.
     *
     * If the requested date cell cannot be found, the first focusable element in the view is focused.
     * @param {Date} [date=this.date] The date of the cell to focus
     * @internal
     */
    focusDate(date2 = this.date) {
      var _a6;
      const me = this, element = me.eventContentElement || me.contentElement;
      date2 = me.ingestDate(date2);
      let dayCell = (_a6 = me.getDayElement) == null ? void 0 : _a6.call(me, date2);
      if (dayCell && !DomHelper.isFocusable(dayCell)) {
        dayCell = dayCell.querySelector('[role="link"]');
      }
      if (!dayCell || !DomHelper.isFocusable(dayCell)) {
        dayCell = [...element.querySelectorAll("*")].find(DomHelper.isFocusable);
      }
      (dayCell || me).focus({
        preventScroll: true,
        focusVisible: true
      });
    }
    /**
     * For use by the {@link Calendar.feature.TimeRanges} feature. This yields the set of
     * {@link Calendar.model.TimeRangeModel}s and {@link Scheduler.model.ResourceTimeRangeModel}s
     * to be rendered in the passed date range.
     * @param {Date} startDate The start date of the range to be returned
     * @param {Date} endDate The end date of the range to be returned.
     * @returns {Calendar.model.TimeRangeModel[]}
     * @private
     */
    getTimeRanges(startDate, endDate) {
      const {
        resourceId,
        project
      } = this, includeTimeRanges = resourceId ? this.owner.includeTimeRanges : true, ranges = resourceId == null || includeTimeRanges ? project == null ? void 0 : project.getTimeRanges(startDate, endDate) : [];
      if (resourceId != null) {
        const resourceRanges = project == null ? void 0 : project.getResourceTimeRanges(startDate, endDate).filter((r) => r.resourceId == resourceId);
        resourceRanges.forEach((r) => {
          if (!r.color) {
            r.color = this.resource.eventColor;
          }
        });
        ranges.push(...resourceRanges);
      }
      return ranges;
    }
    updateIncludeTimeRanges() {
      if (!this.isConfiguring) {
        this.refresh();
      }
    }
    onConfigChange(info) {
      var _a6;
      if ((_a6 = this.autoRefresh) == null ? void 0 : _a6[info == null ? void 0 : info.name]) {
        this.refreshSoon();
      }
      super.onConfigChange(info);
    }
    changeAvatarRendering(avatarRendering) {
      return AvatarRendering.new({
        element: this.element
      }, avatarRendering);
    }
    updateShowResourceAvatars(showResourceAvatars) {
      if (showResourceAvatars) {
        this.avatarRendering || (this.avatarRendering = true);
      }
      this.refresh();
    }
    getResourceAvatar(resourceRecord) {
      return this.avatarRendering.getResourceAvatar({
        resourceRecord,
        imageUrl: resourceRecord.image === false ? null : resourceRecord.imageUrl || resourceRecord.image && this.resourceImagePath + resourceRecord.image,
        color: resourceRecord.eventColor,
        initials: resourceRecord.initials,
        dataset: {
          btip: StringHelper.encodeHtml(resourceRecord.name),
          resourceId: resourceRecord.id
        }
      });
    }
    updateEventHeight(eventHeight) {
      const { style } = this.element;
      this._eventHeightInPixels = null;
      style.setProperty("--event-height", DomHelper.setLength(eventHeight));
      style.setProperty("--arrow-width", "calc(var(--event-height) / 3)");
      style.setProperty("--arrow-margin", "calc(var(--event-height) / -3)");
      if (!this.isConfiguring) {
        this.refreshSoon();
      }
    }
    /**
     * Returns the pixel value of the {@link #config-eventHeight} in case it was configured as a
     * CSS measurement in other units.
     * @private
     */
    get eventHeightInPixels() {
      const me = this, { eventHeight } = me;
      let eventHeightInPixels = me._eventHeightInPixels;
      if (eventHeight !== "auto") {
        if (!eventHeightInPixels) {
          eventHeightInPixels = me._eventHeight;
          if (typeof eventHeightInPixels === "string") {
            eventHeightInPixels = DomHelper.measureSize(eventHeightInPixels, me.contentElement.querySelector(`.${me.eventBarContainerCls}`), false);
          }
          me._eventHeightInPixels = eventHeightInPixels;
        }
      }
      return eventHeightInPixels;
    }
    /**
     * This property yields the base selector to use to find visible cell elements in this view.
     *
     * It's based upon the {@link #property-dayCellCls}, but also takes into account the
     * {@link #config-hideNonWorkingDays} setting.
     *
     * If this is a MonthView, it also takes into account the
     * {@link Calendar.widget.MonthView#config-hideOtherMonthCells} setting.
     * @property {String}
     * @readonly
     */
    get visibleCellSelector() {
      const excludes = [];
      if (this.hideOtherMonthCells) {
        excludes.push(`.${this.otherMonthCls}`);
      }
      if (this.hideNonWorkingDays) {
        excludes.push(`.${this.nonWorkingDayCls}`);
      }
      return `.${this.dayCellCls}${excludes.length ? `:not(${excludes.join(",")})` : ""}`;
    }
    /**
     * This property yields this widget. This is to enable Calendar Features to be able to attach
     * to standalone Calendar widgets as their owning client, and to access a currently active view
     * in a standard way.
     * @property {Calendar.widget.mixin.CalendarMixin}
     * @typings {typeof CalendarMixin}
     * @readonly
     * @internal
     */
    get activeView() {
      return this;
    }
    /**
     * This property yields this widget. This is to enable Calendar Features to be able to attach
     * to standalone Calendar widgets as their owning client, and to access a currently active view
     * in a standard way.
     * @property {Calendar.widget.mixin.CalendarMixin}
     * @typings {typeof CalendarMixin}
     * @readonly
     * @internal
     */
    get activeSubView() {
      var _a6, _b2;
      const activeSubView = (_b2 = (_a6 = this.items) == null ? void 0 : _a6.filter(isFocusedCalendarMixin)) == null ? void 0 : _b2[0];
      return activeSubView || this;
    }
    /**
     * Calendar mode that this view represents (eg. "day", "month" etc). Only accessible when used within a Calendar.
     * @member {String} modeName
     * @readonly
     */
    /**
     * This function allows a Calendar widget to act as a Feature host by exposing the same interface
     * as a {@link Calendar.view.Calendar}. It executes the passed function on this widget.
     * @internal
     * @param {Function} fn The function to call.
     * @param {Object[]} [args] The arguments to pass. Defaults to this view.
     * @param {Object} [thisObj] The `this` reference for the function. Defaults to this view.
     */
    eachView(fn2, args, thisObj = null) {
      this.callback(fn2, thisObj || this, args || [this]);
    }
    get focusElement() {
      const { calendar } = this;
      if (calendar) {
        const { navigator: navigator2 } = calendar;
        return navigator2.activeItem || navigator2.previousActiveItem || this.element.querySelector(navigator2.itemSelector) || super.focusElement;
      }
    }
    captureFocusItem(activeElement) {
      const activeEvent = this.getEventRecord(activeElement), base = super.captureFocusItem(activeElement);
      return (scrollIntoView = true) => {
        const newEl = activeEvent && this.getEventElement(activeEvent);
        if (newEl) {
          scrollIntoView ? newEl.focus() : newEl.focus({ preventScroll: true });
        } else {
          base == null ? void 0 : base(scrollIntoView);
        }
      };
    }
    /**
     * Refreshes the UI after a change to the EventStore, or to a configuration that requires
     * the UI to change.
     *
     * Only updates the UI if this widget is visible. If it is not visible, the refresh is
     * deferred until it next becomes visible.
     */
    refresh() {
      this.refreshSoon.cancel();
      this.month && this.element.style.setProperty("--week-length", this.month.weekLength);
      this.whenVisible("refreshNow");
    }
    refreshNow() {
      const refocus = this.captureFocus();
      this.doRefresh();
      refocus();
    }
    /**
     * Executes the passed callback after the next refresh, but waits only for a maximum number of
     * milliseconds before optionally performing a refresh and executing the callback.
     *
     * When awaited, the function resolves after the refresh or timeout and after any callback have been executed
     * and the Promise yields `true` if a refresh has been performed.
     *
     * @param {String|Function} [callback] A function or the name of a function in the ownership hierarchy
     * to run after the next refresh operation. May be omitted, and `options` passed as the sole parameter.
     * @param {Object} [options] How long to wait and what to do on timeout.
     * @param {Number} [options.delay=100] The number of milliseconds to wait for a refresh.
     * @param {Boolean} [options.forceRefresh=false] If the refresh does not happen within the timer, call refresh.
     * @async
     * @internal
     */
    afterRefresh(callback, options = { delay: 100, forceRefresh: false }) {
      if (typeof callback === "object") {
        options = callback;
        callback = null;
      } else if (callback) {
        callback = this.resolveCallback(callback, this);
        callback = callback.handler.bind(callback.thisObj);
      }
      if (typeof options === "number") {
        options = { delay: options };
      }
      return new Promise((resolve) => {
        this.ion({
          // expires uses the event name or `name` as the timeout id
          // so in order to be unique, we need to give this a name
          name: this.constructor.generateId("refresh-expiry-timer-"),
          refresh: () => {
            callback == null ? void 0 : callback();
            resolve(true);
          },
          once: true,
          expires: {
            delay: options.delay || 100,
            alt: () => {
              options.forceRefresh && this.refresh();
              callback == null ? void 0 : callback();
              resolve(options.forceRefresh);
            }
          }
        });
      });
    }
    get displayName() {
      return StringHelper.capitalize(this._displayName || this.title || this.type);
    }
    get hasNonWorkingDays() {
      return Boolean(Object.values(this.nonWorkingDays || emptyObject16).some((n) => n)) && !(this.isDayView && this.duration === 1);
    }
    get hiddenNonWorkingDays() {
      return this.hideNonWorkingDays ? this.nonWorkingDays || this.month.nonWorkingDays : emptyObject16;
    }
    changeAutoCreate(autoCreate) {
      if (autoCreate) {
        const type = typeof autoCreate;
        if (type !== "object") {
          autoCreate = {
            gesture: type === "string" ? autoCreate : "dblclick"
          };
        }
        autoCreate = Config.merge(autoCreate, _a5.$meta.config.autoCreate);
        autoCreate.durationUnit = Duration.from(autoCreate.duration).unit;
      }
      return autoCreate;
    }
    updateDateSeparator() {
      this.refreshCalendarDescription();
    }
    updateDescriptionFormat() {
      this.refreshCalendarDescription();
    }
    refreshCalendarDescription() {
      const { calendar } = this;
      if ((calendar == null ? void 0 : calendar.isPainted) && calendar.activeView === this) {
        calendar.updateViewDescription();
      }
    }
    changeShortEventDuration(shortEventDuration) {
      return isNaN(shortEventDuration) ? DateHelper.as("ms", shortEventDuration) : Number(shortEventDuration);
    }
    updateShortEventDuration() {
      if (!this.isConfiguring) {
        this.setTimeout({
          fn: "whenVisible",
          delay: 200,
          cancelOutstanding: true,
          args: ["refreshNow"]
        });
      }
    }
    updateLocalization() {
      if (!("weekStartDay" in this.initialConfig)) {
        this.weekStartDay = DateHelper.weekStartDay;
      }
      if (!("nonWorkingDays" in this.initialConfig)) {
        this.nonWorkingDays = DateHelper.nonWorkingDays;
      }
      super.updateLocalization();
      this.refreshCalendarDescription();
    }
    updateAutoCreate(autoCreate) {
      this.updateLocalization();
    }
    updateWeekStartDay(weekStartDay) {
      var _a6;
      const { refreshCount, month: month2 } = this;
      (_a6 = super.updateWeekStartDay) == null ? void 0 : _a6.call(this, weekStartDay);
      if (month2) {
        month2.weekStartDay = weekStartDay;
      }
      if (this.isPainted && this.refreshCount === refreshCount) {
        this.refresh();
      }
    }
    changeNonWorkingDays(nonWorkingDays) {
      const me = this, result = new Proxy(ObjectHelper.assign({}, nonWorkingDays), {
        set(target) {
          const result2 = Reflect.set(...arguments);
          me.updateNonWorkingDays(target);
          return result2;
        },
        deleteProperty(target) {
          const result2 = Reflect.deleteProperty(...arguments);
          me.updateNonWorkingDays(target);
          return result2;
        }
      });
      return result;
    }
    updateNonWorkingDays(nonWorkingDays) {
      var _a6;
      const { refreshCount, month: month2 } = this;
      (_a6 = super.updateNonWorkingDays) == null ? void 0 : _a6.call(this, nonWorkingDays);
      if (month2) {
        month2.nonWorkingDays = nonWorkingDays;
      }
      if (this.isPainted && this.refreshCount === refreshCount) {
        this.refresh();
      }
    }
    dayOfDate(date2) {
      return DateHelper.clearTime(date2);
    }
    ingestDate(date2) {
      date2 = typeof date2 === "string" ? DateHelper.parse(date2) : new Date(date2);
      if (isNaN(date2)) {
        throw new Error("Calendar widget date ingestion must be passed a Date, or a YYYY-MM-DD date string");
      }
      return this.dayOfDate(date2);
    }
    changeDate(date2, oldDate) {
      const me = this, { isEventList } = me, incomingDate = me.ingestDate(date2), dateContainmentFn = isEventList && !me.range ? "betweenLesserEqual" : "betweenLesser";
      date2 = me.constrainTargetDate(incomingDate);
      if (arguments.length === 1) {
        return date2;
      }
      if (!oldDate || date2 - oldDate) {
        if (me.trigger("beforeDateChange", { date: date2, oldDate }) !== false) {
          const dateDiff = DateHelper.diff(incomingDate, date2, "d");
          if (dateDiff && DateHelper[dateContainmentFn](date2, me.startDate, me.endDate)) {
            if (isEventList && me.rowManager.rowCount) {
              const row = me[dateDiff < 0 ? "lastFullyVisibleRow" : "firstFullyVisibleRow"], constrainedDate = row && me.getDateFromElement(row.cells[0]);
              if (constrainedDate) {
                date2 = constrainedDate;
              }
            }
            me._date = date2;
          } else {
            return date2;
          }
        }
      }
    }
    constrainTargetDate(date2, end) {
      var _a6, _b2;
      const me = this, minDate = me.minDate || ((_a6 = me.calendar) == null ? void 0 : _a6.minDate), maxDate = me.maxDate || ((_b2 = me.calendar) == null ? void 0 : _b2.maxDate);
      if (!me.isInCoerceTargetDate && (!isNaN(minDate) || !isNaN(maxDate))) {
        me.isInCoerceTargetDate = true;
        const newDate = end ? me[`change${StringHelper.capitalize(end)}Date`](date2, null) : date2;
        me.isInCoerceTargetDate = false;
        if (!isNaN(minDate) && newDate < minDate) {
          return minDate;
        }
        if (!isNaN(maxDate) && newDate > maxDate) {
          return maxDate;
        }
      }
      return date2;
    }
    changeStartDate(startDate) {
      if (startDate) {
        return this.ingestDate(startDate);
      }
    }
    changeEndDate(endDate) {
      if (endDate) {
        return this.ingestDate(endDate);
      }
    }
    /**
     * Brings an event or a time into view. Optionally visually highlights the target.
     *
     * __This may change the date range encompassed by this view to bring the date or event into its
     * ownership__.
     *
     * Scrolling may or may not be required, depending on the type and size constraints of the view.
     *
     * Base scrolling options are added from the {@link #config-defaultScrollOptions} config.
     *
     * @param {Scheduler.model.EventModel|Date} target The event or Date to scroll to.
     * @param {Object} [options] How to scroll.
     * @param {'start'|'end'|'center'|'nearest'} [options.block] How far to scroll the target.
     * @param {Number} [options.edgeOffset] edgeOffset A margin around the target to bring into view.
     * @param {Object|Boolean|Number} [options.animate] Set to `true` to animate the scroll by 300ms,
     * or the number of milliseconds to animate over, or an animation config object.
     * @param {Number} [options.animate.duration] The number of milliseconds to animate over.
     * @param {String} [options.animate.easing] The name of an easing function.
     * @param {Boolean|Function} [options.highlight] Set to `true` to highlight the resulting element
     * when it is in view. May be a function which is called passing the resulting element
     * to provide customized highlighting.
     * @param {Boolean} [options.focus] Set to `true` to focus the element when it is in view.
     * @param {Boolean} [options.x] Pass as `false` to disable scrolling in the `X` axis.
     * @param {Boolean} [options.y] Pass as `false` to disable scrolling in the `Y` axis.
     * @returns {Promise} A promise which is resolved when the target has been scrolled into view.
     */
    async scrollTo(target, options) {
      const me = this, { scrollable } = me;
      await me.scrollPromise;
      options = ObjectHelper.assign({}, me.defaultScrollOptions, typeof options == "boolean" ? { animate: options } : options);
      if (target.isEvent) {
        const eventRecord = target;
        if (!DateHelper.intersectSpans(me.startDate, me.endDate, target.startDate, target.endDate)) {
          me.date = target.startDate;
        }
        target = me.getEventElement(target);
        if (!target) {
          me.refresh();
          target = me.getEventElement(eventRecord);
        }
      } else {
        target = me.ingestDate(target);
        if (!DateHelper.betweenLesser(target, me.startDate, me.endDate) || !me.getDayElement(target, true)) {
          me.date = target;
        }
        target = me.getDayElement(target);
      }
      if (target == null) {
        return;
      }
      if (scrollable) {
        me.scrollPromise = scrollable.scrollIntoView(target, options).then(() => me.scrollPromise = null);
      } else if (options.highlight) {
        if (typeof options.highlight === "boolean") {
          DomHelper.highlight(Rectangle.from(target));
        } else {
          me.callback(options.highlight, me, [target, me]);
        }
      }
      return me.scrollPromise;
    }
    async checkAutoCreateGesture(domEvent, date2, resourceRecord) {
      var _a6;
      const me = this, { autoCreate } = me;
      if (date2 && me.isVisible && !me.readOnly && domEvent.type === ((_a6 = autoCreate == null ? void 0 : autoCreate.gesture) == null ? void 0 : _a6.toLowerCase())) {
        const dateStart = DateHelper.startOf(date2, void 0, void 0, me.weekStartDay), startHourMS = isNaN(autoCreate.startHour) ? DateHelper.getTimeOfDay(DateHelper.parse(autoCreate.startHour, "HH:mm:ss")) : autoCreate.startHour * 1e3 * 60 * 60;
        if (me.trigger("beforeAutoCreate", {
          view: me,
          domEvent,
          date: me.isDayView ? DateHelper[autoCreate.snapType](date2, autoCreate.step) : DateHelper.add(dateStart, startHourMS),
          resourceRecord
        }) !== false) {
          return me.createEvent(date2, resourceRecord, true);
        }
      }
    }
    /**
     * Creates an event on the specified date which conforms to this view's {@link #config-autoCreate}
     * setting.
     *
     * This method may be called programmatically by application code if the `autoCreate` setting
     * is `false`, in which case the default values for `autoCreate` will be used.
     *
     * If the {@link Calendar.feature.EventEdit EventEdit} feature is active, the new event
     * will be displayed in the event editor.
     * @param {Date} date The date to add the event at. If there's no time component, the
     * {@link #config-autoCreate}'s `startHour` will be used.
     */
    createEvent(date2, resourceRecord, isAutoCreate) {
      var _a6;
      const handler = this.calendar || ((_a6 = this.owner) == null ? void 0 : _a6.calendar) || this;
      handler.doCreateEvent(date2, resourceRecord, this, isAutoCreate);
    }
    async doCreateEvent(date2, resourceRecord, editingView = this, isAutoCreate) {
      var _a6;
      resourceRecord = resourceRecord != null ? resourceRecord : this.defaultCalendar;
      const me = this, { isDayView } = me.viewType || me, calendar = me.calendar || ((_a6 = me.owner) == null ? void 0 : _a6.calendar), eventStore = (calendar == null ? void 0 : calendar.eventStore) || me.eventStore, autoCreate = editingView.autoCreate || editingView.changeAutoCreate(true), { modelClass } = eventStore, { newName } = autoCreate;
      if (date2 == null || !isDayView && isAutoCreate) {
        const { dayStartHour, dayEndHour } = me.activeView, startHour = Math.min(Math.max(autoCreate.startHour, dayStartHour), dayEndHour), pickedValue = startHour === dayEndHour ? dayStartHour : startHour, startHourMS = isNaN(pickedValue) ? isNaN(autoCreate.startHour) ? DateHelper.getTimeOfDay(DateHelper.parse(autoCreate.startHour, "HH:mm:ss")) : autoCreate.startHour * 36e5 : pickedValue * 36e5;
        date2 = DateHelper.add(date2 || me.date, startHourMS);
      }
      const d = DateHelper.parseDuration(autoCreate.duration), dateStart = new Date(date2.getTime() + (me.dayStartShift || 0)), startDate = isDayView ? DateHelper[autoCreate.snapType](date2, autoCreate.step) : dateStart, duration = isDayView && isAutoCreate ? {
        magnitude: me.dayTime.startShift ? Math.min(d.magnitude, DateHelper.diff(startDate, DateHelper.add(me.dayTime.startOfDay(date2), me.dayTime.duration(), "ms"), d.unit)) : Math.min(d.magnitude, DateHelper.diff(startDate, DateHelper.add(DateHelper.startOf(date2), me.dayTime.timeEnd), d.unit)),
        unit: d.unit
      } : d, endDate = DateHelper.add(startDate, duration.magnitude, duration.unit), name = me.resolveCallback(newName, me, false) ? me.callback(newName, me, [me, startDate, resourceRecord]) : newName, recordData = {
        [modelClass.getFieldDataSource("name")]: name,
        [modelClass.getFieldDataSource("startDate")]: startDate,
        [modelClass.getFieldDataSource("endDate")]: endDate,
        [modelClass.getFieldDataSource("duration")]: duration.magnitude,
        [modelClass.getFieldDataSource("durationUnit")]: duration.unit,
        // If the view's settings resulted in a midnight to midnight event, flag it as allDay
        allDay: DateHelper.diff(startDate, endDate, "day") === 1
      }, dateContainmentFn = editingView.isEventList && !editingView.range ? "betweenLesserEqual" : "betweenLesser", newRecord = eventStore.createRecord(recordData);
      if ((calendar == null ? void 0 : calendar.features.eventEdit) && !calendar.features.eventEdit.disabled) {
        newRecord.isCreating = true;
      }
      if (resourceRecord) {
        eventStore.assignmentStore.assignEventToResource(newRecord, resourceRecord);
      }
      await eventStore.addAsync(newRecord);
      if (DateHelper[dateContainmentFn](startDate, editingView.startDate, editingView.endDate)) {
        if (editingView.getEventElement(newRecord, startDate)) {
          editingView.editAutoCreatedEvent(newRecord);
        } else {
          editingView.ion({
            refresh({ source }) {
              var _a7;
              (_a7 = source.editAutoCreatedEvent) == null ? void 0 : _a7.call(source, newRecord);
            },
            once: true,
            prio: -1e4,
            buffer: 100,
            expires: 500
          });
        }
      }
    }
    editAutoCreatedEvent(eventRecord) {
      this.trigger("eventAutoCreated", {
        eventRecord
      });
    }
    /**
     * The duration in total days that this view covers.
     * @property {Number}
     * @readonly
     */
    get duration() {
      return this.endDate ? this.calculateDuration(this.startDate, this.endDate) : 1;
    }
    calculateDuration(startDate, endDate) {
      return DateHelper.diff(startDate, endDate, "day");
    }
    /**
     * Moves this view forwards in time by its configured (or intrinsic if it's a
     * {@link Calendar.widget.WeekView} or a {@link Calendar.widget.YearView}) duration.
     */
    next() {
      this.date = DateHelper.add(this.date, this.duration, "day");
    }
    /**
     * Moves this view backwards in time by its configured (or intrinsic if it's a
     * {@link Calendar.widget.WeekView} or a {@link Calendar.widget.YearView}) duration.
     */
    previous() {
      this.date = DateHelper.add(this.date, -this.duration, "day");
    }
    get eventContentElement() {
      return this.contentElement;
    }
    /**
     * The number of __visible__&nbsp;events that this view currently displays in its date range.
     *
     * Filtered out events are not incuded in the count.
     *
     * Events from {@link Calendar.widget.DayView#config-dateFilter filtered out dates} are not included in the count.
     *
     * Events which are in {@link #config-hideNonWorkingDays hidden non-working days} are
     * __not__&nbsp;included in the count.
     *
     * This value may be used when creating a view description string.
     * @member {Number}
     * @readonly
     */
    get eventCount() {
      var _a6;
      const me = this, {
        dateFilter,
        hiddenNonWorkingDays
      } = me;
      if (me.views) {
        return me.views.reduce((r, c) => r += c.eventCount || 0, 0);
      }
      if (me.cellMap) {
        return [...me.cellMap.values(), ...((_a6 = me.allDayEvents) == null ? void 0 : _a6.cellMap.values()) || []].reduce((r, c) => {
          if ((!dateFilter || me.callback(dateFilter, me, [c])) && !hiddenNonWorkingDays[c.day]) {
            r += c.events.length;
          }
          return r;
        }, 0);
      }
      return me.eventStore.getEvents({
        startDate: me.startDate,
        endDate: me.endDate
      }).length;
    }
    /**
     * The first *visible* event-bearing element in this view. So if the first day defined in the
     * range is a Sunday, and {@link #config-hideNonWorkingDays} is set, then the first visible
     * cell will be for the Monday.
     * @property {HTMLElement}
     */
    get firstVisibleCell() {
      return this.eventContentElement.querySelector(this.visibleCellSelector);
    }
    /**
     * The last *visible* event-bearing element in this view. So if the last day defined in the
     * range is a Sunday, and {@link #config-hideNonWorkingDays} is set, then the last visible
     * cell will be for the Friday.
     * @property {HTMLElement}
     */
    get lastVisibleCell() {
      const visibleCells = this.contentElement.querySelectorAll(this.visibleCellSelector);
      return visibleCells[visibleCells.length - 1];
    }
    /**
     * The date of the first *visible* event-bearing element in this view. So if the first day defined
     * in the range is a Sunday, and {@link #config-hideNonWorkingDays} is set, then the first visible
     * date will be the date of the Monday.
     * @property {Date}
     */
    get firstVisibleDate() {
      const me = this, date2 = new Date(me.startDate), { month: month2 } = me.month;
      let { duration } = me;
      while (duration && (me.hideOtherMonthCells && date2.getMonth() !== month2 || me.hiddenNonWorkingDays[date2.getDay()])) {
        date2.setDate(date2.getDate() + 1);
        duration--;
      }
      return date2;
    }
    /**
     * The date of the last *visible* event-bearing element in this view. So if the last day defined
     * in the range is a Sunday, and {@link #config-hideNonWorkingDays} is set, then the last visible
     * date will be the date of the Friday.
     * @property {Date}
     */
    get lastVisibleDate() {
      const me = this, date2 = DateHelper.add(me.endDate, -1, "d"), { month: month2 } = me.month;
      let { duration } = me;
      while (duration && (me.hideOtherMonthCells && date2.getMonth() !== month2 || me.hiddenNonWorkingDays[date2.getDay()])) {
        date2.setDate(date2.getDate() - 1);
        duration--;
      }
      return date2;
    }
    updateHideNonWorkingDays(hideNonWorkingDays) {
      var _a6, _b2;
      const me = this, {
        month: month2,
        calendar
      } = me;
      (_a6 = me.contentElement) == null ? void 0 : _a6.classList[hideNonWorkingDays ? "add" : "remove"](me.hideNonWorkingDaysCls);
      if (month2 == null) {
        return;
      }
      let activeColumnIndex, date2, activeDay;
      if (!me.isConfiguring && hideNonWorkingDays) {
        date2 = me.date;
        activeDay = date2 == null ? void 0 : date2.getDay();
        if (date2 && me.nonWorkingDays[activeDay] && me.getDayElement(date2)) {
          activeColumnIndex = month2.visibleDayColumnIndex[activeDay];
        }
      }
      month2.hideNonWorkingDays = hideNonWorkingDays;
      (_b2 = super.updateHideNonWorkingDays) == null ? void 0 : _b2.call(this, hideNonWorkingDays);
      if (typeof activeColumnIndex === "number") {
        const weekStart = month2.getWeekStart(month2.getWeekNumber(date2)), newActiveColumnIndex = Math.min(activeColumnIndex, month2.visibleColumnCount - 1);
        for (let i = -1; ; weekStart.setDate(weekStart.getDate() + 1)) {
          if (typeof month2.visibleDayColumnIndex[weekStart.getDay()] === "number") {
            if (++i === newActiveColumnIndex) {
              break;
            }
          }
        }
        me.date = weekStart;
        calendar && (calendar.date = date2);
      }
    }
    onCalendarStoreChange({ source, action }) {
      var _a6;
      const me = this;
      if (action === "dataset" && !source.isChained) {
        return;
      }
      if (me.project.isInitialCommitPerformed && !me.project.isWritingData && me.project.isEngineReady()) {
        (_a6 = me._cellMap) == null ? void 0 : _a6.clear();
        if (me.isVisible && me.allowTransitionsOnRefresh == null) {
          me.allowTransitionsOnRefresh = true;
        }
        me.refreshSoon();
      }
    }
    /**
     * Schedules a refresh of the UI for the next animation frame. This is a useful method to call when
     * making multiple data changes, so that each change merely *schedules* a refresh for the next AF and
     * DOM churn is kept to a minimum.
     *
     * Calling {@link #function-refresh} directly cancels any scheduled refresh operation and updates
     * the UI immediately
     */
    refreshSoon() {
      this.refresh();
    }
    /**
     * Called when new event is created.
     * Сan be overridden to supply default record values etc.
     * @param {Scheduler.model.EventModel} eventRecord Newly created event
     */
    onEventCreated(eventRecord) {
    }
    /**
     * Returns the event record for a DOM element or DOM event.
     * @param {HTMLElement|Event} elementOrEvent The DOM node to lookup, or a DOM event whose target to lookup.
     * @returns {Scheduler.model.EventModel} The event record
     */
    getEventRecord(elementOrEvent) {
      var _a6;
      let element = elementOrEvent instanceof Event ? elementOrEvent.target : elementOrEvent;
      element = (_a6 = element == null ? void 0 : element.closest) == null ? void 0 : _a6.call(element, "[data-event-id]");
      return element && this.eventStore.getById(element.dataset.eventId);
    }
    /**
     * Returns the resource record for a DOM element or DOM event if the element is inside a view
     * which displays events for one resource such as a {@link Calendar.widget.ResourceView}
     * or a {@link Calendar.widget.DayResourceView}.
     * @param {HTMLElement|Event} elementOrEvent The DOM node to lookup, or a DOM event whose target to lookup.
     * @returns {Scheduler.model.ResourceModel} The resource record
     */
    getResourceRecord(elementOrEvent) {
      let element = elementOrEvent instanceof Event ? elementOrEvent.target : elementOrEvent;
      element = (element == null ? void 0 : element.closest("[data-resource-id]")) || null;
      return element && this.resourceStore.getById(element.dataset.resourceId);
    }
    /**
     * Returns the event record for a DOM element or DOM event.
     * @param {HTMLElement|Event} elementOrEvent The DOM node to lookup, or a DOM event whose target to lookup.
     * @returns {Scheduler.model.EventModel} The event record
     */
    resolveEventRecord(elementOrEvent) {
      return this.getEventRecord(elementOrEvent);
    }
    getDateFromElement(element, keyParser = null, raw = false) {
      var _a6;
      let dateElement = element == null ? void 0 : element.closest("[data-date],[data-header-date],[data-month-date]");
      if (dateElement) {
        const rawDate = dateElement.dataset.date || dateElement.dataset.headerDate || dateElement.dataset.monthDate;
        return raw ? rawDate : (keyParser || (this.isDayView ? this.dayTime : DateHelper)).parseKey(rawDate);
      }
      dateElement = element == null ? void 0 : element.closest("[data-week]");
      if (dateElement) {
        return (_a6 = this.month) == null ? void 0 : _a6.getWeekStart(dateElement.dataset.week.split(",").map(Number));
      }
    }
    getDateFromDomEvent(domEvent) {
      return this.getDateFromElement(DomHelper.getEventElement(domEvent));
    }
    getDateFromPosition() {
      return null;
    }
    dateKey(date2) {
      return DateHelper.makeKey(date2);
    }
    /**
     * Returns the cell associated with the passed date.
     *
     * In certain views, the strict definition if whether the view owns the date may be optionally enforced.
     *
     * For example, in a YearView or MonthView, dates outside the configured year or month may be displayed.
     *
     * To exclude these, pass the `strict` parameter as `true`
     * @param {Date|String} date The date to find the element for or a key in the format `YYYY-MM-DD`
     * @param {Boolean} strict Only return the element if this view *owns* the date. (MonthView and YearView)
     */
    getDayElement(date2, strict) {
      if (typeof date2 !== "string") {
        date2 = this.dateKey(date2);
      }
      return this.eventContentElement.querySelector(`[data-date="${date2}"]`);
    }
    // Used by DayView and CalendarRow to see which day cell the X position relates to.
    getDayElementFromX(x) {
      const dayCells = this.eventContentElement.querySelectorAll("[data-date]");
      for (let rect, el, i = 0, { length } = dayCells; i < length; i++) {
        rect = (el = dayCells[i]).getBoundingClientRect();
        if (x >= rect.x && x <= rect.x + rect.width) {
          return el;
        }
      }
      return dayCells[0];
    }
    /**
     * Returns the outermost element which represents the first block of the passed event in the view. *If the
     * event is represented within the view*.
     *
     * *Note* if the event covers multiple weeks, this will only return the first element.
     *
     * To return all elements use {@link #function-getEventElements}.
     *
     * To return an event element at a particular date, pass the date as the second parameter.
     * @param {Scheduler.model.EventModel|String|Number} eventRecord The event, or event ID to find the element for.
     * @param {Date} [date] Optionally, the event element at the specified date.
     * @returns {HTMLElement} The first element which corresponds to the event. Note that *some* views,
     * such as {@link Calendar.widget.MonthView MonthView} and {@link Calendar.widget.CalendarRow CalendarRow}
     * may render multiple elements for long events.
     */
    getEventElement(eventRecord, date2 = Math.max(eventRecord.startDate, this.firstVisibleDate || this.startDate)) {
      var _a6;
      const me = this, activeEventElement = (_a6 = me.calendar) == null ? void 0 : _a6.navigator.activeItem, activeDate = me.getDateFromElement(activeEventElement), eventId = me.eventStore.modelClass.asId(eventRecord);
      if (document.contains(activeEventElement) && (activeEventElement == null ? void 0 : activeEventElement.dataset.eventId) === String(eventId) && (activeDate && !(date2 - activeDate))) {
        return activeEventElement;
      }
      if (date2) {
        const dayCell = me.getDayElement(date2);
        if (dayCell) {
          return DomHelper.down(dayCell, `[data-event-id="${eventId}"]`);
        }
      }
      return me.getEventElements(eventRecord)[0];
    }
    /**
     * Returns all outermost elements which represents the passed event in the view. *If the
     * event is represented within the view*
     * @param {Scheduler.model.EventModel|String|Number} eventRecord The event, or event ID to find the elements for.
     * @returns {HTMLElement[]} The elements which corresponds to the event. Note that *some* views,
     * such as {@link Calendar.widget.MonthView MonthView} and {@link Calendar.widget.CalendarRow CalendarRow}
     * may render multiple elements for long events.
     */
    getEventElements(eventRecord) {
      const eventId = this.eventStore.modelClass.asId(eventRecord);
      return this.eventContentElement.querySelectorAll(`[data-event-id="${eventId}"]`);
    }
    onEventMouseOverOut(domEvent) {
      const me = this, {
        currentOverEventEl
      } = me, isOut = domEvent.type === "mouseout", toElement = domEvent[isOut ? "relatedTarget" : "target"], toEventEl = (toElement == null ? void 0 : toElement.closest(".b-cal-event-wrap")) || null, isChange = toEventEl !== (currentOverEventEl || null);
      if (isChange) {
        if (isOut) {
          me.currentOverEventEl = null;
          if (currentOverEventEl) {
            Object.defineProperty(domEvent, "target", {
              configurable: true,
              get: () => currentOverEventEl
            });
          }
          return me.onCalendarPointerInteraction(domEvent);
        } else {
          me.currentOverEventEl = toEventEl;
          return me.onCalendarPointerInteraction(domEvent);
        }
      }
    }
    onEventMouseEnterLeave(domEvent) {
      if (domEvent.target.classList.contains("b-cal-event")) {
        return this.onCalendarPointerInteraction(domEvent);
      }
    }
    /**
     * Determines what is under the cursor of the specified event or what is described by the given element.
     * @param {Event|Element} domEvent The event or element
     * @returns {CalendarHit}
     */
    calendarHitTest(domEvent) {
      const me = this, { monthSelector } = me, date2 = me.getDateFromDomEvent(domEvent), target = DomHelper.getEventElement(domEvent);
      let ret = null, closest, eventRecord;
      if ((closest = target.closest(".b-cal-event-wrap")) && (eventRecord = me.eventStore.getById(closest.dataset.eventId))) {
        ret = {
          type: "event",
          eventElement: closest,
          eventRecord
        };
      } else if (closest = target.closest(me.dayNameSelector)) {
        ret = {
          type: "dayNumber",
          dayNumberElement: closest
        };
      } else if (closest = target.closest(".b-week-num,.b-week-number-cell")) {
        const weekElement = target.closest(".b-calendar-week");
        if (weekElement && weekElement.dataset.week) {
          ret = {
            type: "weekNumber",
            week: weekElement.dataset.week.split(",").map(Number),
            weekNumberElement: closest,
            weekElement
          };
        }
      } else if (monthSelector && target.closest(monthSelector)) {
        ret = {
          type: "monthName",
          month: date2.getMonth(),
          date: date2
        };
      }
      if (!ret) {
        if (closest = target.closest(".b-cal-cell-overflow")) {
          ret = {
            type: "cellOverflow",
            cellOverflowElement: closest
          };
        } else if (date2) {
          ret = {
            type: "schedule"
          };
        }
      }
      if (ret) {
        ret.resource = me.getResourceRecord(domEvent);
        ret.cell = target.closest(".b-calendar-cell");
        ret.date = date2;
        ret.view = me;
      }
      return ret;
    }
    onCalendarPointerInteraction(domEvent) {
      const me = this, { monthSelector } = me, { target } = domEvent, fromOverflowPopup = Boolean(target.closest(".b-overflowpopup")), domEventName = eventNameMap3[domEvent.type], eventWrap = target.closest(".b-cal-event-wrap"), eventRecord = eventWrap ? me.eventStore.getById(eventWrap.dataset.eventId) : me.getEventRecord(target), multiDay = !fromOverflowPopup && me.isDayCellRenderer && (eventRecord == null ? void 0 : eventRecord.isInterDay), date2 = !domEvent.key && multiDay ? me.getDateFromXY([domEvent.clientX, domEvent.clientY]) : me.getDateFromDomEvent(domEvent), eventElement = eventWrap || eventRecord && me.getEventElement(eventRecord, date2), resourceElement = target.closest("[data-resource-id]"), resourceRecord = resourceElement && me.resourceStore.getById(resourceElement.dataset.resourceId);
      let result;
      if (resourceRecord) {
        result = me.trigger(`resource${domEventName}`, {
          domEvent,
          date: date2,
          eventElement,
          eventRecord,
          resourceRecord,
          fromOverflowPopup
        });
      }
      if (target.closest(me.dayNameSelector)) {
        result = me.trigger(`dayNumber${domEventName}`, {
          domEvent,
          date: date2,
          cellData: me.cellMap.get(date2) || me.createCellData(date2),
          resourceRecord,
          fromOverflowPopup
        });
        if (result === false) {
          return result;
        }
      }
      if (!fromOverflowPopup && !me.eventContentElement.contains(target)) {
        return;
      }
      if (result !== false && eventRecord) {
        const eventResult = me.trigger(`event${domEventName}`, {
          domEvent,
          date: date2,
          eventElement,
          eventRecord,
          resourceRecord,
          fromOverflowPopup
        });
        if (eventResult) {
          result = eventResult;
        }
      }
      if (eventRecord) {
        return result;
      }
      if (target.closest(".b-week-num,.b-week-number-cell")) {
        const weekElement = domEvent.target.closest("[data-week]");
        if (weekElement) {
          return me.trigger(`weekNumber${domEventName}`, {
            domEvent,
            week: weekElement.dataset.week.split(",").map(Number),
            date: me.getDateFromElement(weekElement.querySelector(".b-calendar-cell")),
            fromOverflowPopup
          });
        }
      }
      if (monthSelector && target.closest(monthSelector)) {
        return me.trigger(`monthName${domEventName}`, {
          domEvent,
          month: date2.getMonth(),
          date: date2,
          fromOverflowPopup
        });
      }
      if (target.closest(".b-cal-cell-overflow")) {
        if (me.trigger(`cellOverflow${domEventName}`, {
          domEvent,
          date: date2,
          fromOverflowPopup,
          resourceRecord
        }) !== false) {
          return;
        }
      }
      if (date2 && me.dayCellCls && domEvent.target.closest(`.${me.dayCellCls}`)) {
        result = me.trigger(`schedule${domEventName}`, {
          domEvent,
          date: date2,
          fromOverflowPopup,
          resourceRecord
        });
        if (result === false) {
          return result;
        }
      }
      me.checkAutoCreateGesture(domEvent, me.getDateFromDomEvent(domEvent, true), resourceRecord || void 0);
      return result;
    }
    /**
     * The key handler for CTRL+Z undo key combination. If {@link Calendar.view.Calendar#config-enableUndoRedoKeys} is enabled,
     * the last data change will be undone.
     * @internal
     */
    undoRedoDataChange(keyEvent) {
      var _a6, _b2, _c2;
      if ((_a6 = this.calendar) == null ? void 0 : _a6.enableUndoRedoKeys) {
        return (_c2 = (_b2 = this.project) == null ? void 0 : _b2.stm) == null ? void 0 : _c2.onUndoKeyPress(keyEvent);
      }
    }
    onCalendarKeyDown(keyEvent) {
      this.onCalendarPointerInteraction(keyEvent);
    }
    isAllDayEvent(eventRecord) {
      return eventRecord.allDay || (eventRecord.isScheduled && this.dayTime ? this.dayTime.isInterDay(eventRecord) : eventRecord.isInterDay);
    }
    /**
     * Returns `true` if the given date is today in the current time zone.
     * @param {Date} date The date to check.
     * @returns {Boolean} `true` if the date is today, `false` otherwise.
     */
    isToday(date2) {
      return DateHelper.isToday(date2, this.timeZone);
    }
    /**
     * Sort the given array of `events` in the desired order for this view.
     * @param {Scheduler.model.EventModel[]} events
     * @internal
     */
    sortEvents(events) {
      events.sort(this.eventSorter);
    }
    //region Extract configs
    // These functions are not meant to be called by any code other than Base#getCurrentConfig().
    // This excludes project and calendar from being serialized,
    // they are always assigned on creation not actually configurable
    preProcessCurrentConfigs(configs) {
      super.preProcessCurrentConfigs(configs);
      delete configs.calendar;
      delete configs.project;
    }
    // Extracts the current configs for the calendar view, with special handling to exclude project
    getCurrentConfig(options) {
      const result = super.getCurrentConfig(options);
      delete result.project;
      return result;
    }
    //endregion
  }, __publicField(_a5, "$name", "CalendarMixin"), _a5;
};

// lib/Calendar/util/EventSlots.js
var EventSlots = class extends Array {
  constructor() {
    super(...arguments);
    __publicField(this, "firstFreeSlot", 0);
  }
  set(index, event) {
    const me = this;
    if (index == null) {
      return me.add(event);
    } else {
      me[index] = event;
    }
    if (me.firstFreeSlot === index) {
      while (me[me.firstFreeSlot]) {
        me.firstFreeSlot++;
      }
    }
  }
  add(event) {
    const result = this.firstFreeSlot;
    this[result] = event;
    while (this[this.firstFreeSlot]) {
      this.firstFreeSlot++;
    }
    return result;
  }
  setEvents(events) {
    const length = this.length = this.firstFreeSlot = events.length;
    for (let i = 0; i < length; i++) {
      this[i] = events[i];
    }
  }
};
EventSlots._$name = "EventSlots";

// lib/Calendar/widget/mixin/DayCellCollecter.js
var byKey = ({ key: lhs }, { key: rhs }) => {
  return lhs < rhs ? -1 : rhs < lhs ? 1 : 0;
};
var extractEndDate = (e) => e.endDate || DateHelper.add(e.startDate, e.duration, e.durationUnit);
var DayCellCollecter_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    get dayTime() {
      return DayTime.MIDNIGHT;
    }
    get cellMonth() {
      return this._cellMonth || (this._cellMonth = new Month({}));
    }
    changeEventFilter(eventFilter) {
      if (typeof eventFilter === "string") {
        const { handler, thisObj } = this.resolveCallback(eventFilter);
        eventFilter = handler.bind(thisObj);
      }
      return eventFilter;
    }
    updateStackMultiDayEvents() {
      this.whenVisible("refresh");
    }
    createCellMap(getEventsOptions = {}) {
      const me = this, {
        filter: filter2,
        skipPropagate
      } = getEventsOptions, {
        eventFilter,
        cellMonth,
        lastVisibleDate
      } = me, cellMap = getEventsOptions.cellMap || me._cellMap || (me._cellMap = new CellMap());
      if (me.stackMultiDayEvents) {
        me.eventSorter = EventSorter.defaultSorterFn;
      }
      if (lastVisibleDate) {
        lastVisibleDate.setDate(lastVisibleDate.getDate() + 1);
      }
      let startDate = getEventsOptions.startDate || me.firstVisibleDate || me.startDate, endDate = getEventsOptions.endDate || lastVisibleDate || me.endDate;
      cellMonth.configure({
        weekBase: null,
        weekStartDay: me.weekStartDay,
        nonWorkingDays: me.nonWorkingDays,
        hideNonWorkingDays: me.hideNonWorkingDays,
        sixWeeks: me.sixWeeks,
        date: startDate
      });
      if (me.eventStore) {
        getEventsOptions = ObjectHelper.assign({
          dayTime: DayTime.MIDNIGHT
        }, getEventsOptions, {
          filter: filter2 && eventFilter ? (e) => filter2(e) && eventFilter(e) : filter2 || eventFilter,
          dateMap: cellMap,
          startDate,
          endDate
        });
        me.collectEvents(getEventsOptions);
        startDate = getEventsOptions.startDate;
        endDate = getEventsOptions.endDate;
        for (const key of [...cellMap.keys()]) {
          let lastEventEndDate = 0;
          const dayTime = getEventsOptions.dayTime, date2 = dayTime.dayOfDate(DateHelper.parseKey(key)), cellData = me.createCellData(date2), sortEvent2 = {
            events: cellMap.get(key),
            date: date2
          };
          me.sortEvents(sortEvent2.events, date2);
          me.trigger("dayCellPopulated", sortEvent2);
          const events = sortEvent2.events;
          if (getEventsOptions.rawEvents) {
            lastEventEndDate = Math.min(endDate, Math.max.apply(Math, events.map(extractEndDate)));
            cellData.events = events;
          } else {
            cellData.events = events.map((eventRecord) => {
              const eventEndDate = eventRecord.endingDate, overflows = eventEndDate > cellData.tomorrow, eventData = {
                isAllDay: me.isAllDayEvent(eventRecord),
                isOverflow: eventRecord.startDate < cellData.date && date2 - startDate,
                eventRecord,
                eventEndDate,
                overflows,
                date: date2
              };
              if (!skipPropagate) {
                lastEventEndDate = Math.min(endDate, Math.max(lastEventEndDate, eventEndDate));
                if (overflows) {
                  eventData.propagateEndDate = me.calculatePropagateEndDate(eventData, endDate);
                }
              }
              return eventData;
            });
          }
          if (!skipPropagate) {
            for (; date2 < lastEventEndDate; date2.setDate(date2.getDate() + 1)) {
              const key2 = dayTime.dateKey(date2);
              cellMap.has(key2) || cellMap.set(key2, me.createCellData(date2));
            }
          }
          cellMap.set(key, cellData);
        }
        if (cellMap.size) {
          const cellMapEntries = [...cellMap.values()].sort(byKey);
          let previousEvents;
          cellMap.clear();
          cellMapEntries.forEach((entry) => {
            cellMap.set(entry.key, entry);
            if (previousEvents) {
              previousEvents.nextEvents = entry;
              entry.previousEvents = previousEvents;
            }
            previousEvents = entry;
          });
          if (!skipPropagate) {
            me.propagateCellEvents(cellMapEntries[0], cellMap);
          }
        }
      }
      cellMap.populated = true;
      me.trigger("cellMapPopulated", {
        cellMap
      });
      return cellMap;
    }
    /**
     * Calculates the end date (EXCLUSIVE) to which an event must be propagated based upon the
     * event's data in order to create a day-spanning event bar.
     *
     * If an event overflows into 2011-01-02T01:00, then the exclusive propagateEndDate
     * is 2011-01-03T00:00:00 so the event will be propagated into 2011-01-02.
     *
     * But if an event ends on 2011-01-02T00:00:00, its propagateEndDate will be 2011-01-02T00:00:00
     * so it will be propagated as far as 2011-01-01
     *
     * This may be overridden in subclasses to customize how events are propagated forwards.
     *
     * example:
     * ```javascript
     * class OvernightEventMonthView extends MonthView {
     *     static get name() {
     *         return 'OvernightEventMonthView';
     *     }
     *
     *     static get type() {
     *         return 'overnighteventmonthview';
     *     }
     *
     *     calculatePropagateEndDate(eventData) {
     *         // If the event only spills into the next day but not further
     *         // then we do not want an extended event bar.
     *         // It will still get an arrow indicating that it continues rightwards.
     *         if (eventData.eventEndDate < DateHelper.add(eventData.date, 1, 'd')) {
     *             return DateHelper.add(DateHelper.clearTime(eventData.eventRecord.startDate), 1, 'd');
     *         }
     *         // Default case, propagate event into the future as usual
     *         return super.calculatePropagateEndDate(eventData);
     *     }
     * }
     * // Register the type name
     * OvernightEventMonthView.initClass();
     *
     * new Calendar({
     *     modes : {
     *         // Use our MonthView subclass as the month mode.
     *         month : {
     *             type :'overnighteventmonthview'
     *         }
     *     }
     * })
     * ```
     *
     * Note that this is implemented by both {@link Calendar.widget.MonthView} and {Calendar.widget.CalendarRow}
     * which is the "all day" row in a week or day view.
     *
     * @param {Object} eventData A data block describing the time context of an event.
     * @param {Date} eventData.eventEndDate The end date for which to calculate the propagate end date.
     * @param {Boolean} eventData.isAllDay `true` if the event is an all day event, or spans multiple days.
     * @param {Boolean} eventData.isOverflow `true` if this is being called as part of further propagation.
     * @param {Boolean} eventData.overflows `true` if the event extends into future cells.
     * @param {Scheduler.model.EventModel} eventData.eventRecord The event record being propagated.
     * @param {Date} eventData.date The date from which the event is being propagated.
     * @returns {Date} The date (as a timepoint, *not* a reference to a 24 hour time block)
     * to which the event bar should be propagated
     * @internal
     */
    calculatePropagateEndDate(eventData, viewEndDate = this.endDate) {
      const { eventEndDate: endDate } = eventData, startOfDay = this.dayTime.startOfDay(endDate);
      eventData.propagateEndDate = new Date(Math.min(endDate > startOfDay ? DateHelper.add(startOfDay, 1, "day") : endDate, viewEndDate));
      this.trigger("eventPropagate", eventData);
      return eventData.propagateEndDate;
    }
    // Overrideable in subclasses.
    collectEvents(options) {
      if (!options.skipPropagate) {
        options.propagateRecurringEvents = false;
      }
      return this.eventStore.getEvents(options);
    }
    propagateCellEvents(cellData, cellMap) {
      var _a6;
      const me = this, {
        events,
        renderedEvents,
        previousEvents,
        nextEvents,
        date: date2
      } = cellData, {
        stackMultiDayEvents
      } = me, eventsPerCell = me.getEventsPerCell(date2), { length } = events;
      for (let i = 0; i < length; i++) {
        const event = events[i], {
          eventRecord,
          propagateEndDate,
          eventEndDate
        } = event;
        let renderedSlot = renderedEvents.add(event);
        if (event.overflows) {
          let isVisible2 = cellData.visible, lastEvent2;
          for (let nextDay = nextEvents; nextDay && nextDay.date < propagateEndDate; nextDay = nextDay.nextEvents) {
            if (!nextDay.columnIndex || stackMultiDayEvents) {
              renderedSlot = nextDay.renderedEvents.firstFreeSlot;
            }
            nextDay.renderedEvents.set(renderedSlot, lastEvent2 = {
              eventRecord,
              eventEndDate,
              propagateEndDate,
              isAllDay: event.isAllDay,
              isOverflow: isVisible2,
              overflows: true
            });
            isVisible2 = isVisible2 || nextDay.visible;
          }
          lastEvent2 && (lastEvent2.overflows = false);
        }
      }
      cellData.maxRow = renderedEvents.length;
      const lastEvent = renderedEvents[eventsPerCell - 1];
      if (renderedEvents.length === eventsPerCell) {
        if ((lastEvent == null ? void 0 : lastEvent.isOverflow) && (previousEvents == null ? void 0 : previousEvents.hasOverflow)) {
          cellData.maxRow--;
          cellData.hasOverflow = true;
        }
      } else if (renderedEvents.length > eventsPerCell) {
        cellData.maxRow = eventsPerCell - 1;
        cellData.hasOverflow = true;
        if ((lastEvent == null ? void 0 : lastEvent.isOverflow) && !me.hiddenNonWorkingDays[cellData.day]) {
          const weekStartValue = ((_a6 = me.getWeekContext) == null ? void 0 : _a6.call(me, date2).visibleWeekStart) || me.firstVisibleDate, eventStartValue = DateHelper.clearTime(lastEvent.eventRecord.startDate).valueOf();
          let originatingCell = cellMap.get(DateHelper.makeKey(new Date(Math.max(weekStartValue, eventStartValue))));
          while (originatingCell.key !== cellData.key) {
            originatingCell.hasOverflow = true;
            originatingCell.maxRow = eventsPerCell - 1;
            originatingCell = originatingCell.nextEvents;
          }
        }
      }
      nextEvents && me.propagateCellEvents(nextEvents, cellMap);
    }
    createCellData(date2) {
      return Object.assign(this.cellMonth.getCellData(date2, this.month, this.dayTime), {
        view: this,
        events: [],
        // This property only exists in subviews of ResourceView
        resource: this.resource,
        // Events can forward-occupy slots if they
        // overrun their start day.
        // So the next step is to propagate forward
        // multi day events into future cells they cover.
        renderedEvents: new EventSlots()
      });
    }
  }, __publicField(_a5, "$name", "DayCellCollecter"), __publicField(_a5, "configurable", {
    /**
     * A function, or the name of a function in the ownership hierarchy to filter which events
     * are collected into the day cell data blocks.
     * Return `true` to include the passed event, or a *falsy* value to exclude the event.
     *
     * @config {Function|String}
     * @param {Scheduler.model.EventModel} event the passed event
     * @returns {Boolean}
     */
    eventFilter: {
      $config: "lazy",
      value: null
    },
    /**
     * Set to `true` to stack multi-day events within each cell in the order of their start time.
     *
     * When this property is set, multi day events will not sort to the top of day cells and flow
     * in the same row from cell to cell.
     *
     * Rather, the bar in the start cell will be sorted by its start time. In future cells, its
     * bar will be sorted to the top of the cell.
     * @prp {Boolean} stackMultiDayEvents
     * @default false
     */
    stackMultiDayEvents: null
  }), _a5;
};
var CellMap = class extends Map {
  constructor() {
    super(...arguments);
    __publicField(this, "populated", false);
  }
  get(d, value2) {
    return super.get(DateHelper.makeKey(d), value2);
  }
  set(d, value2) {
    d = DateHelper.makeKey(d);
    if (!this.has(d)) {
      this.generation = (this.generation || 0) + 1;
    }
    return super.set(d, value2);
  }
  delete(d, value2) {
    d = DateHelper.makeKey(d);
    if (this.has(d)) {
      this.generation++;
    }
    return super.delete(d, value2);
  }
  has(d) {
    return super.has(DateHelper.makeKey(d));
  }
  clear() {
    if (this.populated) {
      this.populated = false;
      this.generation = (this.generation || 0) + 1;
      return super.clear();
    }
  }
};

// lib/Calendar/widget/OverflowPopup.js
var OverflowPopup = class extends Popup {
  calendarHitTest(domEvent) {
    const target = DomHelper.getEventElement(domEvent), closest = target.closest(".b-cal-event-wrap");
    let eventRecord;
    if (closest && (eventRecord = this.owner.eventStore.getById(closest.dataset.eventId))) {
      return {
        eventRecord,
        type: "event",
        eventElement: closest,
        view: this
      };
    }
    return null;
  }
  onPanelHeaderClick({ event: domEvent }) {
    var _a5;
    if (domEvent.target.closest(".b-header-title")) {
      (_a5 = this.owner) == null ? void 0 : _a5.trigger("dayNumberClick", {
        domEvent,
        date: this.activeDate,
        cellData: this.cellData,
        source: this.owner,
        fromOverflowPopup: true
      });
    } else {
      super.onPanelHeaderClick(...arguments);
    }
  }
  get focusElement() {
    var _a5, _b2;
    const activeElement = DomHelper.getActiveElement(this);
    return this.element.contains(activeElement) ? activeElement : ((_b2 = (_a5 = this.eventList) == null ? void 0 : _a5.element) == null ? void 0 : _b2.firstElementChild) || super.focusElement;
  }
  getDateFromPosition() {
    return this.activeDate;
  }
  getEventElement(eventRecord) {
    return this.contentElement.querySelector(`[data-event-id="${eventRecord.id}"]`);
  }
  showOverflow(cell, cellData) {
    var _a5;
    const me = this;
    const date2 = me.activeDate = cellData.date;
    me.cellData = cellData;
    me.targetCell = cell;
    if (cellData.resource) {
      me.element.dataset.resourceId = cellData.resource.id;
    }
    me.refresh(cellData);
    me.showBy(cell);
    if (me.isVisible) {
      (_a5 = me.owner) == null ? void 0 : _a5.trigger("showOverflowPopup", { cell, cellData, date: date2, overflowPopup: me });
    }
  }
  // Owning view calls this from its own onCalendarStoreChange implementation
  onCalendarStoreChange() {
    const me = this;
    if (me.isVisible) {
      me.cellData = me.owner.cellMap.get(DateHelper.makeKey(me.activeDate));
      me.refresh(me.cellData);
    }
  }
  refresh(cellData) {
    if (!this.eventList) {
      return;
    }
    if (cellData) {
      this.cellData = cellData;
    } else {
      cellData = this.cellData;
    }
    const me = this, {
      owner,
      eventRenderer,
      eventSorter
    } = me, { element } = me.eventList, slicePoint = me.onlyShowOverflow ? Math.max(owner.eventsPerCell - 1, 0) : 0, events = cellData == null ? void 0 : cellData.renderedEvents.slice(slicePoint), children = [];
    if (!(events == null ? void 0 : events.length) || !(cellData == null ? void 0 : cellData.hasOverflow) && !owner.isYearView) {
      return me.hide();
    }
    if (eventSorter) {
      events.sort(eventSorter);
    }
    for (let i = 0, eventRow = 0, { length } = events; i < length; i++, eventRow++) {
      const renderedEvent = events[i];
      if (renderedEvent) {
        const eventDomConfig = owner.createEventDomConfig(renderedEvent, eventRenderer);
        Object.assign(eventDomConfig.className, {
          // The event started in a previous day
          "b-continues-past": renderedEvent.eventRecord.startDate < cellData.date,
          // The event ends in a future day
          "b-continues-future": renderedEvent.eventRecord.endDate > cellData.tomorrow
        });
        eventDomConfig.style.marginBottom = `${owner.eventSpacing}px`;
        children.push(eventDomConfig);
      }
    }
    DomSync.sync({
      domConfig: {
        children
      },
      targetElement: element
    });
    element.dataset.date = cellData.key;
    element.classList.add(`b-day-of-week-${cellData.day}`);
    element.classList.remove(`b-day-of-week-${me.lastRefreshDay}`);
    me.lastRefreshDay = cellData.day;
    me.realign();
  }
  changeItems(items2) {
    const { eventList } = this;
    if (eventList) {
      items2.eventList = eventList;
    }
    const result = super.changeItems(items2);
    if (eventList) {
      this.eventList = this.widgetMap.eventList;
    }
    return result;
  }
  changeActiveDate(activeDate, oldActiveDate) {
    activeDate = typeof date === "string" ? DateHelper.parse(activeDate) : new Date(activeDate);
    if (isNaN(activeDate)) {
      throw new Error("OverflowPopup date ingestion must be passed a Date, or a YYYY-MM-DD date string");
    }
    return activeDate;
  }
  updateActiveDate(activeDate) {
    this.element.dataset.date = DateHelper.makeKey(activeDate);
    this.title = `<span tabIndex="0" role="link" aria-label="${DateHelper.format(activeDate, "dddd, D MMMM")}">${DateHelper.format(activeDate, this.dateFormat)}</span>`;
  }
  onDocumentMouseDown({ event }) {
    var _a5;
    const alignedTo = (_a5 = this.lastAlignSpec) == null ? void 0 : _a5.target, { target } = event;
    if (alignedTo == null ? void 0 : alignedTo.contains(target)) {
      const cellOverflowButton = target.closest(".b-cal-cell-overflow");
      if (target.closest(".b-cal-event-bar-container") || target.matches(".b-cal-cell-overflow") || (cellOverflowButton == null ? void 0 : cellOverflowButton.children.length) === 1) {
        return event.preventDefault();
      }
    }
    super.onDocumentMouseDown(...arguments);
  }
};
__publicField(OverflowPopup, "$name", "OverflowPopup");
// Factoryable type name
__publicField(OverflowPopup, "type", "overflowpopup");
__publicField(OverflowPopup, "configurable", {
  defaultFocus: ".b-cal-event-wrap",
  textContent: false,
  autoShow: false,
  floating: false,
  cls: "b-cal-event-list",
  closable: true,
  draggable: {
    handleSelector: ":not(.b-cal-event-wrap)"
  },
  anchor: true,
  layout: "vbox",
  /**
   * An empty function by default, but provided so that you can override it.
   *
   * This function is called each time an event is rendered to allow developers to mutate
   * the cell metadata, or the CSS classes to be applied to the event element.
   *
   * It's called with the event record, and a eventData object which allows you to mutate event metadata
   * such as 'cls', 'style'.
   *
   * The cls property is an object whose property names will be added to the event element if the property
   * value is truthy.
   *
   * The style property is an object containing style properties for the event element.
   *
   * A non-null return value from the renderer is used as the event body content. A nullish
   * return value causes the default renderer to be used which just uses the event name.
   *
   * If a string is returned, it is used as the HTML content of the event body element.
   *
   * If an object is returned, it is used as a {@link Core.helper.DomHelper#typedef-DomConfig} object to
   * create complex content in the event body element.
   *
   * ```javascript
   *  eventRenderer({ eventRecord, renderData }) {
   *      if (eventRecord.name === 'Doctors appointment') {
   *          renderData.style.fontWeight = 'bold';
   *          renderData.cls['custom-cls'] = 1;
   *
   *          return 'Special doctors appointment';
   *      }
   *  }
   * ```
   * IMPORTANT: When returning content, be sure to consider how that content should be encoded to avoid XSS
   * (Cross-Site Scripting) attacks. This is especially important when including user-controlled data such as
   * the event's `name`. The function {@link Core.helper.StringHelper#function-encodeHtml-static} as well as
   * {@link Core.helper.StringHelper#function-xss-static} can be helpful in these cases.
   *
   * For example:
   * ```javascript
   *  eventRenderer({ eventRecord }) {
   *      return StringHelper.xss`Event: ${eventRecord.name}`;
   *  }
   * ```
   * @config {Function} eventRenderer
   * @param {Object} detail An object that contains data about the event being rendered.
   * @param {Scheduler.model.EventModel} detail.eventRecord The event record
   * @param {Scheduler.model.ResourceModel} detail.resourceRecord The event record
   * @param {Object} detail.renderData A data object containing properties that will be used to create the event element.
   * @param {Object} detail.renderData.style The style property is an object containing style properties for
   * the event element.
   * @param {Object} detail.renderData.cls The cls property is an object whose property names will be added to
   * the event element if the property value is truthy.
   * @param {String} detail.renderData.eventColor Color to be applied to the event
   * @returns {String|DomConfig|DomConfig[]|null} A simple string, or a DomConfig object defining the actual HTML
   */
  eventRenderer: null,
  /**
   * A function which compares events to decide upon rendering order.
   *
   * By default, this class uses the sorted order of its owning view and does *not* perform
   * a sort, but this may be configured to override that behaviour.
   *
   * Note that the two objects to compare may either be {@link Scheduler.model.EventModel}s
   * or {@link EventBar}s which __contain__ an `eventRecord` property which is the {@link Scheduler.model.EventModel}.
   * @config {Function}
   * @param {Scheduler.model.EventModel|EventBar} lhs The left side value to conpare.
   * @param {Scheduler.model.EventModel|EventBar} rhs The right side value to conpare.
   * @returns {Number}
   */
  eventSorter: null,
  /**
   * A {@link Core.widget.Widget} config object used to show the inner list of overflowing events
   * shown in this popup.
   *
   * This widget has a {@link Core.widget.Widget#config-weight} of `500`, so to insert widgets above
   * this, use `weight` less than 500, and to insert widgets below it use `weight` greater than 500.
   * @config {ContainerItemConfig}
   * @accepts {Object}
   * @default
   */
  eventList: {
    type: "widget",
    cls: "b-cal-event-bar-container",
    weight: 500
  },
  items: {},
  tools: {
    maximize: null
  },
  /**
   * The {@link Core.helper.DateHelper#function-format-static DateHelper} format string to use to
   * create the {@link #config-title} of this dialog.
   * @config {String}
   * @default
   */
  dateFormat: "dddd, MMM DD",
  activeDate: {
    $config: {
      equal: "date"
    },
    value: null
  },
  scrollAction: "realign",
  align: {
    axisLock: "flexible",
    constrainPadding: 20,
    minHeight: 400
  },
  scrollable: {
    overflowY: "auto"
  },
  /**
   * By default an event overflow popup shows all the events for the activated date.
   *
   * Configure this as `true` to only display the events which were hidden due to overflow.
   * @config {Boolean}
   */
  onlyShowOverflow: null
});
OverflowPopup.initClass();
OverflowPopup._$name = "OverflowPopup";

// lib/Calendar/widget/mixin/DayCellRenderer.js
var emptyFn5 = () => {
};
var { eventNameMap: eventNameMap4 } = EventHelper;
var DayCellRenderer_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    static get configurable() {
      return {
        monitorResize: true,
        // we need to adjust the "+ 2 more" overflow indicator
        /**
         * This may be specified to create the content for the date and day name section of calendar cells.
         *
         * This function is called for each cell rendered. It can return an HTML string which will become the
         * content of the header section of the cell above any events.
         *
         * It can also return a {@link Core.helper.DomHelper#typedef-DomConfig} element creation object (or array of
         * same) to specify content to create in the header section of the cell.
         *
         * It also allows developers to mutate the cell metadata, or the CSS classes to be applied to the cell.
         *
         * The {@link Core.helper.DomHelper#typedef-DomConfig} definition passed as the first parameter may be
         * mutated to create a different cell header.
         *
         * ```javascript
         * dayCellRenderer : function(cellData, dayCellDomConfig) {
         *     // I don't like Mondays!
         *     if (cellData.day === 1) {
         *         dayCellDomConfig.className['hackathon-dayoff'] = true;
         *         dayCellDomConfig.style.fontWeight = 'bold';
         *
         *         cellData.isNonWorking = true;
         *
         *         return `${cellData.date.getDate()} Day off yay!`;
         *     }
         * }
         * ```
         *
         * A non-null return value from the renderer is used as the content of the day number element.
         *
         * @config {Function} dayCellRenderer
         * @param {DayCell} cellData A `DayCell` object that contains data about the cell..
         * @param {DomConfig|Object} dayCellDomConfig An object to sync the day cell element.
         * @param {Object} dayCellDomConfig.className An object who's truthy property names will be applied as class
         *   names.
         * @param {Object} dayCellDomConfig.style A CSS style definition object.
         * @param {Object} dayCellDomConfig.dataset The DOM data properties to set.
         * @param {DomConfig[]} dayCellDomConfig.children The {@link DomConfig} definitions for the content of the
         * cell.
         * @returns {String|Object|Object[]} The definition of the cell header content.
         */
        dayCellRenderer: null,
        // Must still be visible if dynamically set to non working to keep
        // column arrangement correct.
        dynamicHideNonWorkingdayCls: "b-dynamic-nonworking-day",
        /**
         * The {@link Calendar.widget.OverflowPopup} instance that this view may show when events
         * for one day overflow the available space.
         * @member {Calendar.widget.OverflowPopup} overflowPopup
         * @readonly
         */
        /**
         * A config object used to create the {@link Calendar.widget.OverflowPopup} that this view
         * may show when events for one day overflow the available space.
         *
         * For example
         *
         * ```javascript
         *     modes : {
         *         month : {
         *             overflowPopup : {
         *                 closable   : false,
         *                 dateFormat : 'dddd, MMM M',
         *                 eventRenderer({ eventRecord, renderData }) {
         *                     if (calendarUtils.isImportantEvent(eventRecord)) {
         *                         // Add CSS class to important events
         *                         renderData.cls['b-important'] = 1;
         *                     }
         *                 }
         *             }
         *         }
         *     }
         * ```
         *
         * @config {OverflowPopupConfig}
         */
        overflowPopup: {
          $config: ["lazy", "nullify"],
          value: {
            type: "overflowpopup"
          }
        },
        /**
         * The pointer gesture which shows the popup containing any overflowing events
         * in the current view.
         *
         * This means events which will not fit into a `MonthView` day cell, or *all* events
         * for a `YearView` cell.
         *
         * Useful values are `'click'` (the default), and `'mouseover'`
         * @config {'click'|'mouseover'|'hover'}
         * @default
         */
        overflowPopupTrigger: "click",
        eventBarContainerCls: "b-cal-event-bar-container",
        /**
         * A function, or name of a function which is passed the {@link DomConfig} object which
         * will be used to create the "+n more" button which indicates that a day cell has
         * overflowing events.
         *
         * ```javascript
         * overflowButtonRenderer : function(domConfig) {
         *     domConfig.className['b-fa'] = domConfig.className['b-fa-list'] = 1;
         *     return domConfig;
         * }
         * ```
         *
         * The result is used to create the overflow button element.
         *
         * To target the element using custom CSS, use the class name `b-cal-cell-overflow`.
         *
         * @config {Function} overflowButtonRenderer
         * @param {DomConfig|Object} domConfig A {@link DomConfig} config object which is used to create the
         *   overflow button.
         * @param {String} domConfig.tag=button The tag name of the element to create.
         * @param {Object} domConfig.className An object who's truthy property names will be applied as class names.
         * @param {String} domConfig.text The inner content of the element. **Note that this
         *   will be HTML encoded for XSS safety**
         * @param {Object} domConfig.style A CSS style definition object.
         * @param {Object} domConfig.dataset The DOM data properties to set.
         * @param {Number} overflowCount The number of overflowing events.
         * @returns {DomConfig|String|null}
         */
        overflowButtonRenderer: null,
        /**
         * The {@link Core.helper.DateHelper} format string to format the day names
         * in the header part of each calendar cell.
         * @config {String}
         */
        dayCellNameFormat: null,
        /**
         * How much vertical space in pixels to leave between event bars in a cell.
         * @prp {Number}
         * @default
         */
        eventSpacing: 2,
        emptyCellCls: "b-cal-empty-cell",
        /**
         * A {@link DomConfig} object which will be used to create the content of a clickable
         * element which is present when no events are in a cell.
         *
         * Or a function, or name of a function which returns a {@link DomConfig} object.
         *
         * See the {@link Calendar.view.Calendar#event-emptyCellClick} event.
         *
         * @config {Function|Object|String} emptyCellRenderer
         * @param {DayCell} dayCellRenderer A `DayCell` object that contains data about the cell.
         * @returns {DomConfig|null} DomConfig object representing the HTML markup
         */
        emptyCellRenderer: null
      };
    }
    onCalendarStoreChange() {
      var _a6;
      super.onCalendarStoreChange(...arguments);
      if ((_a6 = this._overflowPopup) == null ? void 0 : _a6.isVisible) {
        this._overflowPopup.onCalendarStoreChange(...arguments);
      }
    }
    // Called automatically on the CellOverflow${overflowPopupTrigger} event because of callOnFunctions
    onCellOverflowGesture({ domEvent, date: date2 }) {
      const me = this, cellData = me.cellMap.get(DateHelper.makeKey(date2));
      if (cellData) {
        const { overflowPopup } = me, cell = me.getCellFromEvent(domEvent);
        if (overflowPopup && me.trigger("beforeShowOverflowPopup", { cell, cellData, date: date2, overflowPopup })) {
          overflowPopup.showOverflow(cell, cellData);
        }
      }
    }
    getCellFromEvent(domEvent) {
      return domEvent.target.closest(".b-calendar-cell[data-date]");
    }
    changeOverflowPopupTrigger(overflowPopupTrigger) {
      overflowPopupTrigger = overflowPopupTrigger.toLowerCase();
      if (overflowPopupTrigger === "mouseover" || overflowPopupTrigger === "hover") {
        overflowPopupTrigger = "mouseOver";
      }
      return overflowPopupTrigger;
    }
    updateOverflowPopupTrigger(overflowPopupTrigger, was) {
      if (was) {
        this[`onCellOverflow${StringHelper.capitalize(was)}`] = emptyFn5;
      }
      this[`onCellOverflow${StringHelper.capitalize(overflowPopupTrigger)}`] = this.onCellOverflowGesture;
    }
    onEventMouseOverOut({ target, relatedTarget, type }) {
      var _a6;
      if (this.overflowPopupTrigger === "mouseOver" && target.closest(".b-cal-cell-overflow") && !relatedTarget.closest(".b-cal-cell-overflow")) {
        if (type === "mouseover") {
          return this.onCalendarPointerInteraction(...arguments);
        } else {
          (_a6 = this._overflowPopup) == null ? void 0 : _a6.hide();
        }
      }
      return this.onCalendarPointerInteraction(...arguments);
    }
    changeOverflowPopup(config, instance) {
      var _a6;
      const me = this, popupOwner = me.up("calendar") || me.owner || me, ret = OverflowPopup.reconfigure(instance, config, {
        owner: me,
        // so that we'll destroy our instances on reconfigure to null
        defaults: {
          positioned: !(config == null ? void 0 : config.floating),
          owner: me,
          align: {
            // Support Calendar being a child of a Popup
            constrainTo: ((_a6 = me.up("popup")) == null ? void 0 : _a6.contentElement) || globalThis
          }
        }
      });
      if (ret) {
        const { element } = ret, { eventHeightInPixels } = me;
        ret.element.classList.add(`b-${me.type}-overflowpopup`);
        if (ret.positioned) {
          ret.render(popupOwner.isCalendar ? popupOwner.element : popupOwner.contentElement);
        }
        if (eventHeightInPixels) {
          element.style.setProperty("--event-height", `${eventHeightInPixels}px`);
          element.style.setProperty("--arrow-width", `${eventHeightInPixels / 3}px`);
        }
        if (ret !== instance && popupOwner !== me) {
          EventHelper.on({
            thisObj: me,
            element: ret.bodyElement,
            delegate: ".b-cal-event-wrap",
            mouseover: "onEventMouseOverOut",
            mouseout: "onEventMouseOverOut",
            mousedown: "onCalendarPointerInteraction",
            mouseup: "onCalendarPointerInteraction",
            click: "onCalendarPointerInteraction",
            dblclick: "onCalendarPointerInteraction",
            contextmenu: "onCalendarPointerInteraction"
          });
          if (ret.headerElement) {
            EventHelper.on({
              thisObj: me,
              element: ret.headerElement,
              mousedown: "onCalendarPointerInteraction",
              mouseup: "onCalendarPointerInteraction",
              click: "onCalendarPointerInteraction",
              dblclick: "onCalendarPointerInteraction",
              contextmenu: "onCalendarPointerInteraction"
            });
          }
        }
      }
      return ret;
    }
    onInternalResize(element, width, height, oldWidth, oldHeight) {
      const {
        _eventsPerCell,
        _eventContainerTop
      } = this;
      super.onInternalResize(element, width, height, oldWidth, oldHeight);
      if (this.rendered && height !== oldHeight) {
        this.performResizeRefresh(_eventsPerCell, _eventContainerTop);
      }
    }
    performResizeRefresh(prevEventsPerCell, prevEventContainerTop, now2) {
      this._eventsPerCell = this._eventContainerTop = this._eventContainerHeight = null;
      const me = this, {
        eventsPerCell,
        _overflowPopup
      } = me;
      if (eventsPerCell !== prevEventsPerCell) {
        me.trigger("eventsPerCellChange", {
          value: eventsPerCell,
          oldValue: prevEventsPerCell
        });
      }
      if (me.eventContainerTop !== prevEventContainerTop || eventsPerCell !== prevEventsPerCell) {
        now2 ? me.doRefresh() : me.refresh();
        if (_overflowPopup == null ? void 0 : _overflowPopup.isVisible) {
          _overflowPopup.refresh(me._cellMap.get(DateHelper.makeKey(_overflowPopup.activeDate)));
        }
      }
    }
    updateEventHeight() {
      super.updateEventHeight(...arguments);
      this._eventsPerCell = null;
      this.element.style.setProperty("--event-height-px", `${this.eventHeightInPixels}px`);
    }
    updateEventSpacing(eventSpacing) {
      this._eventsPerCell = null;
      if (!this.isConfiguring) {
        this.refreshSoon();
      }
      this.element.style.setProperty("--event-spacing", `${eventSpacing}px`);
    }
    get weeksElement() {
      return this.contentElement;
    }
    get eventContainerHeight() {
      if (this._eventContainerHeight == null && this.isVisible && !this.isConfiguring) {
        const { firstVisibleCell } = this;
        this._eventContainerHeight = firstVisibleCell ? firstVisibleCell.offsetHeight - this.eventContainerTop : NaN;
      }
      return this._eventContainerHeight;
    }
    get eventContainerTop() {
      const me = this;
      if (me._eventContainerTop == null && me.isVisible && !me.isConfiguring) {
        const { firstVisibleCell } = me, c = firstVisibleCell && firstVisibleCell.querySelector(".b-cal-cell-header"), top = c ? c.getBoundingClientRect().height : 0;
        me._eventContainerTop = top === 0 ? null : top;
        me.element.style.setProperty("--event-top", `${me._eventContainerTop}px`);
      }
      return me._eventContainerTop || 0;
    }
    /**
     * Returns the number of complete event bars which will fit inside the referenced cell.
     *
     * The base implementation assumes all cells are the same height. In most views, most of the time,
     * this is true.
     *
     * It's only in MonthView when some rows are shrinkwrapped round their event content (meaning
     * either expanded or contracted away from the 1/6 height default) that there may be a customized
     * eventsPerCell for a certain date. So only MonthView has an overriding implementation.
     * @internal
     */
    getEventsPerCell(date2) {
      return this.eventsPerCell;
    }
    get eventsPerCell() {
      const me = this;
      if (me._eventsPerCell == null) {
        me._eventsPerCell = Math.floor((me.eventContainerHeight + me.eventSpacing) / (me.eventHeightInPixels + me.eventSpacing));
      }
      return me._eventsPerCell;
    }
    get cellMap() {
      var _a6;
      return ((_a6 = this._cellMap) == null ? void 0 : _a6.populated) ? this._cellMap : this.createCellMap();
    }
    cellRenderer({ cell }) {
      const domConfig = this.getCellDomConfig(...arguments);
      DomSync.sync({
        domConfig,
        targetElement: cell
      });
    }
    getCellDomConfig({ cell, columnIndex, row, date: date2, day: day2, dayTime = DayTime.MIDNIGHT, visibleColumnIndex, key }) {
      var _a6;
      let dayName2, dayEvents = !this.isConfiguring && this.cellMap.get(DateHelper.makeKey(date2));
      const me = this, {
        isMonthView: isMonthView2,
        eventHeightInPixels,
        eventSpacing,
        weekLength,
        todayCls,
        emptyCellRenderer,
        dayCellNameFormat,
        resource
      } = me, isToday = me.isToday(date2), todayText = isToday ? me.L("L{Object.today}, ") : "", dayLinkProps = {
        tabIndex: isMonthView2 ? 0 : -1,
        role: isMonthView2 ? "link" : "columnheader",
        "aria-label": `${todayText}${DateHelper.format(date2, "dddd Do MMMM")}${resource ? ` ${resource.name}` : ""}.`,
        dataset: {
          headerDate: key
        }
      }, dayNumber = date2.getDate(), eventContainer = {
        className: me.eventBarContainerCls,
        role: "presentation",
        children: [],
        // Match existing data-event-id elements first and ensure DOM order matches
        // children order.
        syncOptions: {
          syncIdField: "eventId",
          releaseThreshold: 0,
          strict: true
        }
      }, cellHeader = {
        style: {},
        className: new DomClassList({
          "b-cal-cell-header": true,
          [todayCls]: isToday
        })
      }, domConfig = {
        tabIndex: -1,
        role: "gridcell",
        "aria-label": `${todayText}${DateHelper.format(date2, "dddd Do MMMM")}${resource ? ` ${resource.name}` : ""},  ${me.L("eventCount", ((_a6 = (dayEvents == null ? void 0 : dayEvents.renderedEvents) || (dayEvents == null ? void 0 : dayEvents.events)) == null ? void 0 : _a6.length) || 0)}.`,
        dataset: {},
        style: {},
        className: new DomClassList(cell == null ? void 0 : cell.className),
        children: [cellHeader, eventContainer]
      }, isFirstVisibleCell = !(date2 - (me.firstVisibleDate || -1));
      domConfig.className[todayCls] = isToday;
      if (me.addCellHeaderContent) {
        dayName2 = me.addCellHeaderContent(cellHeader, dayEvents || (dayEvents = me.createCellData(date2)));
      } else {
        cellHeader.children = [dayName2 = {
          className: {
            "b-day-name": true
          }
        }];
      }
      if (isMonthView2) {
        Object.assign(dayName2, dayLinkProps);
      } else {
        Object.assign(cellHeader, dayLinkProps);
      }
      let dynamicallySetToNonWorking;
      if (me.dayCellRenderer && !me.isConfiguring) {
        if (!dayEvents) {
          dayEvents = me.createCellData(date2);
        }
        dayEvents.style = domConfig.style;
        dayEvents.cls = domConfig.className;
        dayEvents.headerStyle = cellHeader.style;
        dayEvents.headerCls = cellHeader.className;
        const wasNonWorking = dayEvents && dayEvents.isNonWorking, dayNameContent = me.callback(me.dayCellRenderer, me, [dayEvents, domConfig]);
        if (typeof dayEvents.cls == "string") {
          domConfig.className = new DomClassList(dayEvents.cls);
        }
        if (typeof dayEvents.style === "string") {
          domConfig.style = DomHelper.parseStyle(dayEvents.style);
        }
        if (typeof dayEvents.headerCls == "string") {
          cellHeader.className = new DomClassList(dayEvents.headerCls);
        }
        if (typeof dayEvents.headerStyle === "string") {
          cellHeader.style = DomHelper.parseStyle(dayEvents.headerStyle);
        }
        if (dayNameContent != null) {
          if (typeof dayNameContent === "string") {
            dayName2.html = dayNameContent;
          } else {
            dayName2.text = null;
            dayName2.children = ArrayHelper.asArray(dayNameContent);
          }
        }
        dynamicallySetToNonWorking = !wasNonWorking && dayEvents.isNonWorking;
      }
      if (dayName2) {
        if (!(dayName2.text || dayName2.html)) {
          if (dayCellNameFormat) {
            dayName2.html = DateHelper.format(date2, me.dayCellNameFormat);
          } else {
            dayName2.text = dayNumber;
          }
        }
        if (dayNumber === 1) {
          dayName2.dataset = {
            startOf: DateHelper.format(date2, "MMM")
          };
        }
        if (String(dayName2.text || dayName2.html).endsWith(dayNumber)) {
          dayName2.className["b-day-num"] = true;
        }
      }
      if (dayEvents.visible) {
        const {
          hasOverflow,
          renderedEvents,
          maxRow
        } = dayEvents, {
          stackMultiDayEvents
        } = me, events = renderedEvents, { length } = events, children = eventContainer.children;
        domConfig.className[me.nonWorkingDayCls] = dayEvents.isNonWorking;
        domConfig.className[me.dynamicHideNonWorkingdayCls] = dynamicallySetToNonWorking;
        domConfig.className[me.weekendCls] = DateHelper.weekends[day2];
        let i = 0, eventRow = 0, overflowCount = 0;
        for (; i < maxRow; i++, eventRow++) {
          const renderedEvent = events[i];
          if (renderedEvent) {
            const {
              eventRecord,
              isOverflow,
              propagateEndDate
            } = renderedEvent, eventEndDate = renderedEvent.eventEndDate || eventRecord.endingDate, renderedDaySpan = Math.max(1, me.getDaySpan(date2, propagateEndDate || eventEndDate, dayTime)), daySpan = me.getDaySpan(date2, eventEndDate, dayTime, true), remainingColumns = weekLength - visibleColumnIndex, isHiddenOverflow = !stackMultiDayEvents && visibleColumnIndex && isOverflow, width = DomHelper.percentify(ObjectHelper.round((isHiddenOverflow || stackMultiDayEvents ? 1 : Math.min(renderedDaySpan, remainingColumns)) / weekLength * 100, 4)), eventDomConfig = me.createEventDomConfig(renderedEvent);
            Object.assign(eventDomConfig.style, {
              width,
              top: `${eventRow * eventHeightInPixels + eventRow * eventSpacing + me.eventContainerTop}px`
            });
            Object.assign(eventDomConfig.className, {
              // If flowing in from a previous cell
              "b-overflow": isHiddenOverflow,
              // We're in the first cell or using stackMultiDayEvents, and the event started on an earlier date
              "b-continues-past": !isHiddenOverflow && (!visibleColumnIndex || stackMultiDayEvents || isFirstVisibleCell) && eventRecord.startDate < date2,
              // The event ends in a future week (or a future cell if stackMultiDayEvents)
              "b-continues-future": !isHiddenOverflow && (daySpan > remainingColumns || daySpan > renderedDaySpan || stackMultiDayEvents && daySpan > 1)
            });
            children.push(eventDomConfig);
          }
        }
        while (i < length) {
          if (events[i++]) {
            overflowCount++;
          }
        }
        if (hasOverflow) {
          row.classList.add("b-has-overflow");
          domConfig.children.push(me.getOverflowButtonDomConfig(overflowCount));
        } else if (!columnIndex) {
          row.classList.remove("b-has-overflow");
        }
        const emptyCell = domConfig.className[me.emptyCellCls] = !events.length;
        if (emptyCell && emptyCellRenderer) {
          const rType = typeof emptyCellRenderer, rCallback = rType === "function" || rType === "string" ? me.resolveCallback(emptyCellRenderer, me, false) : null, noEvents = (rCallback == null ? void 0 : rCallback.handler) ? rCallback.handler.call(rCallback.thisObj, dayEvents) : emptyCellRenderer;
          if (noEvents) {
            eventContainer.children[0] = {
              tag: "button",
              class: "b-cal-cell-no-content",
              children: [
                (rCallback == null ? void 0 : rCallback.handler) ? rCallback.handler.call(rCallback.thisObj, dayEvents) : emptyCellRenderer
              ]
            };
          }
        }
      }
      return domConfig;
    }
    onCalendarPointerInteraction(domEvent) {
      const me = this, { target } = domEvent;
      if (target.closest(`.${me.emptyCellCls} .${me.eventBarContainerCls}`)) {
        if (!me.emptyCellRenderer || target.closest(".b-cal-cell-no-content")) {
          if (me.trigger(`emptyCell${eventNameMap4[domEvent.type]}`, {
            date: me.getDateFromDomEvent(domEvent),
            domEvent
          }) === false) {
            return false;
          }
          ;
        }
      }
      return super.onCalendarPointerInteraction(domEvent);
    }
    getDaySpan(startDate, endDate, dayTime, ignoreNonWorkingDays) {
      if (this.hideNonWorkingDays && !ignoreNonWorkingDays) {
        let result = 0;
        for (const date2 = new Date(startDate), spanEnd = dayTime.ceil(endDate, "1 day"); date2 < spanEnd; date2.setDate(date2.getDate() + 1)) {
          if (!this.nonWorkingDays[date2.getDay()]) {
            result++;
          }
        }
        return result;
      } else {
        return Math.max(DateHelper.diff(startDate, dayTime.ceil(endDate, "1 day"), "day"), 1);
      }
    }
    getOverflowButtonDomConfig(overflowCount) {
      const { overflowButtonRenderer } = this, domConfig = {
        tag: "button",
        "aria-label": `${this.L("L{Calendar.moreEvents}", overflowCount)}.`,
        className: {
          "b-cal-cell-overflow": 1
        },
        text: this.L("L{Calendar.plusMore}", overflowCount),
        style: {},
        dataset: {
          eventId: "overflow"
        }
      };
      return overflowButtonRenderer ? this.callback(overflowButtonRenderer, this, [domConfig, overflowCount]) : domConfig;
    }
    getCell(date2) {
      if (typeof date2 !== "string") {
        date2 = this.dayTime.dateKey(date2);
      }
      return this.contentElement.querySelector(`[data-date="${date2}"]`);
    }
    getCellOverflowButton(date2) {
      var _a6;
      return (_a6 = this.getCell(date2)) == null ? void 0 : _a6.querySelector(".b-cal-cell-overflow");
    }
  }, __publicField(_a5, "$name", "DayCellRenderer"), _a5;
};

// lib/Calendar/widget/mixin/DateRangeOwner.js
var validRangeUnits = {
  day: 1,
  week: 1,
  month: 1,
  year: 1,
  decade: 1
};
var compareRange2 = (r1, r2) => r1 && r2 ? r1.unit === r2.unit && r1.magnitude === r2.magnitude : r1 === r2;
var DateRangeOwner_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    /**
     * Interface method used by an encapsulating Calendar view to implement the "prev" button.
     */
    previous() {
      this.move(-1);
    }
    /**
     * Interface method used by an encapsulating Calendar view to implement the "next" button.
     */
    next() {
      this.move(1);
    }
    move(direction = 1) {
      direction = Math.sign(direction);
      const me = this, {
        shiftIncrement,
        range
      } = me;
      if (range) {
        if (shiftIncrement) {
          me.isShifting = true;
          me.date = DateHelper.add(me.date, { unit: shiftIncrement.unit, magnitude: direction * shiftIncrement.magnitude });
          me.isShifting = false;
        }
      } else {
        me.startDate = DateHelper.add(me.startDate, direction * me.duration, "day");
      }
    }
    changeDate(date2, oldDate) {
      date2 = super.changeDate(date2 || this.startDate, oldDate);
      if (this.isConfiguring || this.isValidRange(this.range, date2)) {
        return date2;
      }
    }
    updateDate(date2) {
      var _a6;
      const me = this, newStartDate = me.changeStartDate(date2), {
        startDate,
        _month
      } = me, generation = _month == null ? void 0 : _month.generation;
      if (!startDate || newStartDate - startDate) {
        if (me.hasConfig("endDate") || me.range.unit === "day") {
          const { endDate, duration } = me, dateContainmentFn = me.isEventList && !me.range ? "betweenLesserEqual" : "betweenLesser";
          if (!me.isConfiguring || !startDate || !DateHelper[dateContainmentFn](date2, startDate, endDate)) {
            if (!startDate || !endDate || date2 < startDate) {
              me.startDate = date2;
            } else if (me.isEventList ? date2 > endDate : date2 >= endDate) {
              me.startDate = DateHelper.add(date2, -(duration - 1), "day");
            }
          }
        } else {
          me.startDate = DateHelper.floor(date2, me.isShifting ? me.shiftIncrement : me.range, me.startDate, me.weekStartDay);
        }
      }
      if (_month && _month.generation === generation) {
        _month.date = date2;
      } else if (!me.isConfiguring && me.scrollToDateOnDateChange !== false) {
        me.whenVisible("scrollTo", me, [date2]);
      }
      (_a6 = super.updateDate) == null ? void 0 : _a6.call(this, ...arguments);
    }
    changeStartDate(startDate, oldStartDate) {
      const me = this;
      startDate = super.changeStartDate(me.snapDate(me.ingestDate(startDate)), oldStartDate);
      if (oldStartDate && !(oldStartDate - startDate)) {
        me.afterStartDateSet(startDate, oldStartDate);
      }
      return startDate;
    }
    updateStartDate(startDate, oldStartDate) {
      var _a6;
      const me = this, { _month } = me;
      if (!(me.hasConfig("date") || me.date)) {
        me.date = startDate;
      }
      if (_month) {
        _month.date = startDate;
      }
      (_a6 = super.updateStartDate) == null ? void 0 : _a6.call(this, ...arguments);
      me.afterStartDateSet(startDate, oldStartDate);
    }
    afterStartDateSet(startDate, oldStartDate) {
      var _a6;
      const me = this, {
        refreshCount,
        _endDate
      } = me, incomingEndDate = me.peekEndDate(), endDateSame = incomingEndDate && _endDate && !Boolean(incomingEndDate - _endDate);
      if (me.hasConfig("endDate") && (!incomingEndDate || me.isConfiguring)) {
        const duration = DateHelper.diff(oldStartDate || startDate, me.endDate, "day");
        me.endDate = DateHelper.add(startDate, duration, "day");
      }
      if (!me.isConfiguring && (!incomingEndDate || endDateSame) && me.refreshCount === refreshCount) {
        (_a6 = me._cellMap) == null ? void 0 : _a6.clear();
        me.refresh();
        me.triggerRangeChange(startDate, me.endDate);
      }
    }
    /**
     * Peek at any incoming configured endDate and if there is one, convert it to a Date.
     *
     * startDate, endDate processing may need to know the value of an as yet uningested endDate.
     * @internal
     * @returns {Date} The incoming endDate value from the config object being processed
     */
    peekEndDate() {
      const endDate = this.peekConfig("endDate");
      if (endDate) {
        return this.changeEndDate(endDate);
      }
    }
    updateEndDate(endDate) {
      var _a6, _b2;
      const me = this, { duration } = me;
      (_a6 = super.updateEndDate) == null ? void 0 : _a6.call(this, ...arguments);
      if (!me.isConfiguring) {
        me.element.style.setProperty("--range-magnitude", duration);
        me.element.dataset.duration = duration;
        (_b2 = me._cellMap) == null ? void 0 : _b2.clear();
        me.refresh();
        me.triggerRangeChange(me.startDate, endDate);
      }
    }
    triggerRangeChange(startDate, endDate) {
      const me = this, { lastRangeAnnounced } = me;
      if (!lastRangeAnnounced || lastRangeAnnounced.startDate - startDate || lastRangeAnnounced.endDate - endDate) {
        if (!me.peekConfig("date")) {
          me.date = DateHelper.constrain(me.date, startDate, DateHelper.add(endDate, me.isEventList && !me.range ? 0 : -1, "d"));
        }
        me.trigger("rangeChange", {
          old: lastRangeAnnounced,
          new: me.lastRangeAnnounced = {
            startDate,
            endDate
          }
        });
      }
    }
    get range() {
      return this.hasConfig("endDate") ? null : this._range;
    }
    get shiftIncrement() {
      if (this._shiftIncrement !== null) {
        return this._shiftIncrement || this.range || { magnitude: this.duration, unit: "day" };
      }
    }
    get stepUnit() {
      const { shiftIncrement } = this;
      if (shiftIncrement) {
        return shiftIncrement.magnitude === 1 ? DateHelper.getLocalizedNameOfUnit(shiftIncrement.unit) : `${shiftIncrement.magnitude} ${DateHelper.getLocalizedNameOfUnit(shiftIncrement.unit, true)}`;
      }
    }
    get endDate() {
      const me = this;
      return me.hasConfig("endDate") ? me._endDate : me.startDate && DateHelper.add(me.startDate, me.range.magnitude, me.range.unit);
    }
    // Snap the passed date to the start or end of our configured range block if we have one.
    snapDate(date2, end) {
      const me = this, range = me._endDate || me.peekEndDate() ? null : me.range;
      return range && range.unit !== "day" && date2 ? DateHelper[end ? "ceil" : "floor"](date2, range, void 0, me.weekStartDay) : date2;
    }
    parseRange(range) {
      if (range) {
        if (typeof range === "string") {
          const unit = DateHelper.parseTimeUnit(range);
          if (unit) {
            range = {
              magnitude: 1,
              unit
            };
          } else {
            range = DateHelper.parseDuration(range);
          }
        } else if (typeof range === "number") {
          return {
            magnitude: range,
            unit: "day"
          };
        } else {
          range.unit = DateHelper.parseTimeUnit(range.unit);
        }
        if (!validRangeUnits[range.unit]) {
          throw new Error("Range must be in days, weeks, months, years or decades");
        }
      }
      return range;
    }
    changeRange(range) {
      if (range && !this.hasConfig("endDate")) {
        range = this.parseRange(range);
        if (this._date && !this.isValidRange(range)) {
          return;
        }
      }
      return range;
    }
    changeShiftIncrement(shiftIncrement) {
      return this.changeRange(shiftIncrement);
    }
    updateShiftIncrement(old, shiftIncrement) {
      this.trigger("shiftIncrementChange", {
        old,
        new: shiftIncrement
      });
    }
    isValidRange(range, date2 = this.date) {
      var _a6, _b2;
      const minDate = this.minDate || ((_a6 = this.calendar) == null ? void 0 : _a6.minDate), maxDate = this.maxDate || ((_b2 = this.calendar) == null ? void 0 : _b2.maxDate);
      if (range && !isNaN(minDate) || !isNaN(maxDate)) {
        const newRange = this.calculateDateRange(range, date2);
        if (!isNaN(minDate)) {
          if (newRange.endDate <= minDate) {
            return false;
          }
        }
        if (!isNaN(maxDate)) {
          if (newRange.startDate > maxDate) {
            return false;
          }
        }
      }
      return true;
    }
    updateRange(range) {
      const me = this, {
        date: date2,
        _startDate
      } = me, fixDom = () => {
        const { duration } = me;
        me.element.style.setProperty("--range-magnitude", duration);
        me.element.dataset.duration = duration;
      };
      if (range && date2 && !me.hasConfig("endDate")) {
        if (!_startDate || _startDate - date2) {
          me.startDate = date2;
        } else {
          me.updateStartDate(me._startDate = me.snapDate(_startDate), _startDate);
        }
      }
      if (me.isConfiguring) {
        me.whenVisible(fixDom);
      } else {
        fixDom();
      }
    }
    calculateDateRange(range, date2) {
      if (date2) {
        if (range.unit !== "day") {
          return {
            startDate: DateHelper.floor(date2, range, void 0, this.weekStartDay),
            endDate: DateHelper.ceil(DateHelper.add(date2, 1, "day"), range, void 0, this.weekStartDay)
          };
        }
        return {
          startDate: date2,
          endDate: DateHelper.add(date2, range.magnitude, "day")
        };
      }
    }
    changeMonth(month2) {
      const me = this, { date: date2 } = me;
      if (super.changeMonth) {
        return super.changeMonth(...arguments);
      }
      if (!(month2 == null ? void 0 : month2.isMonth)) {
        month2 = new Month({
          date: date2,
          weekStartDay: me.weekStartDay,
          hideNonWorkingDays: me.hideNonWorkingDays,
          nonWorkingDays: me.nonWorkingDays
        });
        if (me.nonWorkingDays == null) {
          me.nonWorkingDays = month2.nonWorkingDays;
        }
        if (me.weekStartDay == null) {
          me.weekStartDay = month2.weekStartDay;
        }
      }
      return month2;
    }
    /**
     * Returns the range of included dates in the range as a two-element array, i.e., `[0]` is {@link #config-startDate}
     * and `[1]` is {@link #property-lastDate}.
     * @member {Date[]}
     * @internal
     */
    get dateBounds() {
      return [this.startDate, this.lastDate];
    }
    getDuration(startDate = this.startDate) {
      const { range } = this;
      if (range) {
        return startDate ? DateHelper.diff(startDate, DateHelper.add(startDate, range.magnitude, range.unit), "d") : DateHelper.as("d", range.magnitude, range.unit);
      } else {
        return DateHelper.diff(startDate, this.peekEndDate() || this._endDate, "day");
      }
    }
    get duration() {
      return this.getDuration();
    }
    /**
     * The last day that is included in the date range. This is different than {@link #config-endDate} since that date
     * is not inclusive. For example, an `endDate` of 2022-07-21 00:00:00 indicates that the time range ends at that
     * time, and so 2022-07-21 is _not_ in the range. In this example, `lastDate` would be 2022-07-20 since that is the
     * last day included in the range.
     * @member {Date}
     * @internal
     */
    get lastDate() {
      const lastDate = this.endDate;
      return lastDate && DateHelper.add(lastDate, -1, "day");
    }
  }, __publicField(_a5, "$name", "DateRangeOwner"), __publicField(_a5, "configurable", {
    month: true,
    /**
     * Setting this property may change the encapsulated range if the date is outside the current
     * range.
     *
     * It also causes this view to scroll the view to the passed date, or closest date.
     * See {@link Calendar.widget.mixin.CalendarMixin#function-scrollTo}
     * @member {Date} date
     */
    /**
     * The date to orient this view's {@link #config-range} around.
     *
     * When using a {@link #config-range} of weeks, months, years or decades, the {@link #config-startDate} snaps
     * to the closest lower range boundary, and the end date snaps to the closest larger
     * range boundary.
     *
     * When using a {@link #config-range} of days, the {@link #config-startDate} is set to the passed date.
     * @config {Date}
     */
    date: {
      $config: {
        equal: "date"
      },
      value: null
    },
    /**
     * The time range encapsulated by the current {@link #property-date}.
     *
     * When a range is used, changing the {@link #config-date} snaps the {@link #config-startDate}
     * to the closest starting date of the range. For Example if the range was configured as `'1 week'`
     * then setting the date to the date of next Wednesday would mean that the {@link #property-startDate}
     * would be the __start__ of next week, and an entire week would be encapsulated by this view.
     * @member {DurationConfig} range
     * @accepts {String|DurationConfig}
     */
    /**
     * The time range around the {@link #config-date} to display events for.
     *
     * Valid values are:
     * - day
     * - week
     * - month
     * - year
     * - decade
     *
     * This may also be specified as a duration with a magnitude part and a unit part. For
     * example `'1m'` would mean one month, and `'4w'` would mean four weeks.
     * See {@link Core.helper.DateHelper#function-parseDuration-static} for details
     * of syntax.
     *
     * When using a range of weeks, months, years or decades, then when this widget's
     * {@link #config-date} is synced with its owning {@link Calendar.view.Calendar}'s
     * {@link Calendar.view.Calendar#property-date}, this widget's {@link #config-startDate}
     * is snapped to the closest start point of the range which encompasses that date.
     *
     * So if using `range : '1w'`, then setting the date to Thursday, 28th October 2021
     * Would mean that the `startDate` snaps to Sunday 24th October 2021 (assuming the locale
     * uses Sunday as the week start day).
     *
     * If configured to use a range of *days*, no snapping is done. There's no defined start point
     * so the {@link #config-startDate} is set to the incoming Calendar date.
     *
     * __Note:__ If an {@link #config-endDate} is specified, any range is ignored. The encompassed range
     * will be specified by the {@link #config-startDate} and {@link #config-endDate}, and when the
     * {@link #config-startDate} changes, the {@link #config-endDate} is changed to keep the duration
     * the same.
     * @config {String|DurationConfig}
     */
    range: {
      $config: {
        lazy: true,
        equal: compareRange2
      },
      value: null
    },
    /**
     * The time range to move by when {@link #function-next} or {@link #function-previous} is called.
     *
     * If not specified, this view moves by its duration in days as derived from its {@link #property-range}
     * or its {@link #property-startDate} and {@link #property-endDate}.
     *
     * Valid values are:
     * - day
     * - week
     * - month
     * - year
     * - decade
     *
     * This may also be specified as a duration with a magnitude part and a unit part. For
     * example `'1m'` would mean one month, and `'4w'` would mean four weeks.
     * See {@link Core.helper.DateHelper#function-parseDuration-static} for details
     * of syntax.
     *
     * When using a range of weeks, months, years or decades, then when this widget's
     * {@link #config-date} is synced with its owning {@link Calendar.view.Calendar}'s
     * {@link Calendar.view.Calendar#property-date}, this widget's {@link #config-startDate}
     * is snapped to the closest start point of the range which encompasses that date.
     *
     * So if using `range : '1w'`, then setting the date to Thursday, 28th October 2021
     * Would mean that the `startDate` snaps to Sunday 24th October 2021 (assuming the locale
     * uses Sunday as the week start day).
     *
     * If configured to use a range of *days*, no snapping is done. There's no defined start point
     * so the {@link #config-startDate} is set to the incoming Calendar date.
     *
     * Setting this property to `null` disables this view's {@link #function-next} and {@link #function-previous} methods,
     * so effectively disables moving this view in time through the UI. The view can still be moved programmatically
     * by setting its {@link #config-date} or its {@link #config-startDate}.
     *
     * __Note:__ If an {@link #config-endDate} is specified, any range is ignored. The encompassed range
     * will be specified by the {@link #config-startDate} and {@link #config-endDate}, and when the
     * {@link #config-startDate} changes, the {@link #config-endDate} is changed to keep the duration
     * the same.
     * @prp {DurationConfig}
     * @accepts {String|DurationConfig}
     */
    shiftIncrement: {
      $config: {
        lazy: true,
        equal: compareRange2
      },
      value: null
    },
    /**
     * Gets the start date of the {@link #config-range} that this view covers.
     * @member {Date} startDate
     * @readonly
     */
    /**
     * The start date (Time component is zeroed) of this view.
     * @config {Date}
     */
    startDate: {
      $config: {
        equal: "date"
      }
    },
    /**
     * Gets the end date of the {@link #config-range} that this view covers.
     * Note that Date objects are time points, not a representation of a 24 hour period,
     * So `{startDate : '2020-10-24', endDate : '2020-10-25' }` spans the __single__ day
     * 24th October 2020. The end point is `2020-10-25T00:00:00`
     * @member {Date} endDate
     * @readonly
     */
    /**
     * The end date (Time component is zeroed) of this view. Note that in terms of full days,
     * this is exclusive, ie: 2020-01-012 to 2020-01-08 is *seven* days. The end is 00:00:00 on
     * the 8th.
     *
     * __Note:__ This configuration takes precedence over any {@link #config-range} specified.
     * If used, the {@link #config-range} is ignored, and after configuration, the `endDate` is
     * locked to the {@link #config-startDate} when the {@link #config-startDate} is changed.
     * @config {Date}
     */
    endDate: {
      $config: {
        equal: "date"
      }
    },
    /**
     * When using the {@link Calendar.view.Calendar#config-datePicker Calendar's date picker} with the
     * {@link Core.widget.DatePicker#config-multiSelect} property `true`, the selected range is usually
     * applied to views of this type.
     *
     * However, if this property is set to `true`, then the view will not apply the selected
     * range, but will instead use its configured {@link #config-range} or {@link #config-startDate} and
     * {@link #config-endDate} to determine the range of dates to display.
     * @prp {Boolean}
     */
    fixedDuration: null
  }), _a5;
};

// lib/Calendar/widget/CalendarRow.js
var emptyObject17 = Object.freeze({});
var expandGestures = {
  shrinkwrap: 1,
  expand: 1
};
var formatDayPart = (format2, sep, d1, d2, compress) => {
  const s12 = DateHelper.format(d1, format2);
  return d2 ? `${s12}${compress ? sep.trim() : sep}${DateHelper.format(d2, format2).slice(compress ? -1 : 0)}` : s12;
};
var CalendarRow = class extends Widget.mixin(
  CalendarMixin_default,
  DayCellCollecter_default,
  DayCellRenderer_default,
  DateRangeOwner_default
) {
  constructor() {
    super(...arguments);
    /**
     * A Promise which will be in _Pending_ state only when the `minHeight` is animating to a new value.
     * @property {Promise}
     */
    __publicField(this, "heightAnimation", Promise.resolve);
  }
  static get configurable() {
    return {
      // region Hidden configs
      /**
       * @hideconfigs dayCellRenderer
       */
      // endRegion
      cls: "b-calendar-days",
      dragUnit: "day",
      /**
       * Set this to `false` to not use transition for this Widget as it changes height
       *
       * @config {Boolean}
       * @default
       */
      animate: true,
      /**
       * By default, this widget displays a maximum of {@link #config-defaultEventRowCount} events
       * before showing overflow indicators, and becoming {@link #function-toggleExpandCollapse expandable}.
       *
       * Configuring this as `true` makes this widget accommodate all events with no overflow.
       * @config {Boolean}
       */
      autoHeight: null,
      autoRefresh: [
        "dayNameFormat",
        "dayNumberCompress",
        "dayNumberFormat",
        "daySeparator",
        "hideEmptyDays",
        "dateFilter"
      ],
      descriptionFormat: [true, true],
      /**
       * Either the start hour of the day, or a *24 hour* `HH:MM` string denoting the start of the first rendered
       * daily time block.
       *
       * @config {String|Number}
       * @default 0
       */
      dayStartShift: null,
      dayTime: 0,
      /**
       * The number of events to show to define the height of this widget by default if this widget
       * {@link #config-autoHeight} is *not* defined as `true`. The widget may be expanded to accommodate all
       * the events if there are overflowing events.
       * @config {Number}
       * @default
       */
      defaultEventRowCount: 3,
      /**
       * When `true`, a full week of dates is rendered for a single day range. This should not be applied when
       * the `duration` spans multiple days.
       * @prp {Boolean}
       */
      fullWeek: null,
      /**
       * By default, the day columns flex to all fit within the width of this widget.
       *
       * configuring a `minDayWidth` means that if the day columns do not fit within this
       * widget's width, it becomes horizontally scrollable.
       * @config {Number}
       */
      minDayWidth: null,
      scrollable: {
        overflowX: "hidden",
        overflowY: "hidden",
        propagateSync: true
      },
      headerScroller: {
        $config: ["lazy", "nullify"],
        value: {
          overflowX: "hidden",
          overflowY: "hidden",
          propagateSync: true
        }
      },
      /**
       * The {@link Core.helper.DateHelper} format string for day names (e.g., "ddd" for "Mon", "Tue", ...)
       * @config {String}
       * @default
       */
      dayNameFormat: "ddd",
      /**
       * Specify `true` to textually compress day number pairs. For example, compress "11/12" to "11/2". Also
       * when `true`, the {@link #config-daySeparator} is trimmed of any whitespace.
       * @config {Boolean}
       * @default false
       */
      dayNumberCompress: null,
      /**
       * The {@link Core.helper.DateHelper} format string for day numbers (e.g., "D" for "9", "10", ...)
       * @config {String}
       * @default
       */
      dayNumberFormat: "D",
      /**
       * The separator between day names and numbers. If a single string is provided, it applies to both day
       * names and day numbers. If an array of 2 strings is provided, the [0] element is the separator for day
       * names and [1] element is the separator for day numbers.
       * @config {String|String[]}
       */
      daySeparator: "-",
      responsive: {
        small: {
          // dayNameFormat     : 'd1/d1',
          // dayNumberFormat   : 'D/D',
          dayNameFormat: "d1",
          dayNumberCompress: true,
          daySeparator: "/"
        },
        "*": {
          // dayNameFormat     : 'ddd-ddd',
          // dayNumberFormat   : 'D-D',
          dayNameFormat: "ddd",
          dayNumberCompress: false,
          daySeparator: "-"
        }
      },
      dayNameSelector: ".b-cal-cell-header",
      // We are in control of our size. Once we can calculate our maxEventCount, then from
      // that and the eventContainerTop and the defaultEventRowCount and the autoHeight and
      // expanded settings we calculate our height.
      // We have to have been through one initial refresh cycle to measure eventContainerTop
      // which is handled in onInternalPaint.
      monitorResize: false,
      /**
       * How much extra space in pixels or other DOM units to allow below the event bars to
       * facilitate drag-create gestures.
       * @config {Number|String}
       * @default
       */
      gutterHeight: null,
      expanded: null,
      /**
       * How the view responds to clicking on a `+n more` button in an overflowing day cell.
       *
       * The default value, `'popup'`, means that a small dialog box showing the full complement
       * of events for that cell is shown aligned to the cell.
       *
       * When set to `'expand'`, then clicking the `+n more` button causes the encapsulating
       * row to expand to accommodate all events in that row with no overflow.
       *
       * Navigating to a new month resets the row to its default, flexed height.
       * @config {'popup'|'expand'} overflowClickAction
       * @default
       */
      overflowClickAction: "popup",
      /**
       * A function, or name of a function which is passed the {@link DomConfig} object which
       * will be used to sync with a day cell header.
       *
       * ```javascript
       * dayHeaderRenderer : function(headerDomConfig, cellData) {
       *     if (this.isSpecialDate(cellData.date)) {
       *         headerDomConfig.className['b-is-special-date'] = 1;
       *
       *         // Add to the content element's children
       *         headerDomConfig.children.push({
       *             text : 'Special day',
       *             className : 'b-special-day
       *         });
       *     }
       * }
       * ```
       *
       * The result is used to sync the DOM of the day column.
       *
       * @config {Function|String} dayHeaderRenderer
       * @param {DomConfig|Object} headerDomConfig A {@link DomConfig} config object which is used to sync the day header element.
       * @param {Object} headerDomConfig.className An object who's truthy property names will be applied as class names.
       * @param {Object} headerDomConfig.style A CSS style definition object.
       * @param {Object} headerDomConfig.dataset The DOM data properties to set.
       * @param {DomConfig[]} headerDomConfig.children The {@link DomConfig} definitions the header content.
       *   There will be 2 `children` encapsulating the day name and the date.
       * @param {DayCell} cellData A `DayCell` object that contains data about the cell.
       * @returns {String}
       */
      dayHeaderRenderer: null,
      /**
       * Configure this as `true` to hide day columns which contain no events.
       *
       * {@note}Use with care. This may result in no day columns being rendered
       * for completely empty time ranges.{/@note}
       * @prp {Boolean}
       * @default false
       */
      hideEmptyDays: null,
      /**
       * A callback function which an application may provide to filter out day columns for
       * individual dates.
       * @prp {Function|String} dateFilter A function, or the name of a function in the ownership hierarchy.
       * @param {DayCell} context Information about the day column to be created.
       * @returns {Boolean} Return `false` to not display a column for the passed date.
       */
      dateFilter: null
    };
  }
  compose() {
    return {
      children: {
        headerElement: {
          className: "b-calendarrow-header",
          children: {
            headerCellContainer: {
              role: "row",
              className: "b-calendarrow-header-container"
            },
            scrollPadElement: {
              // retainElement : true, // DomSync should leave this alone
              ...DomHelper.scrollBarPadElement
            }
          }
        },
        bodyElement: {
          className: "b-calendarrow-body",
          children: {
            cellContainer: {
              role: "row",
              reference: "cellContainer",
              className: "b-calendarrow-cell-container"
            }
          }
        }
      }
    };
  }
  onInternalPaint({ firstPaint }) {
    if (firstPaint) {
      const me = this, { fonts } = document, onFirstPaint = () => {
        me._eventContainerTop = me._eventContainerHeight = null;
        if (me.eventStore.count) {
          me.performResizeRefresh(null, null, true);
        }
      };
      if ((fonts == null ? void 0 : fonts.status) !== "loaded") {
        fonts.ready.then(() => !me.isDestroyed && onFirstPaint());
      }
      onFirstPaint();
    }
  }
  changeDaySeparator(daySeparator) {
    return Array.isArray(daySeparator) ? daySeparator : [daySeparator, daySeparator];
  }
  changeElement() {
    const result = super.changeElement(...arguments);
    this.scrollable.addPartner(this.headerScroller, "x");
    return result;
  }
  changeHeaderScroller(headerScroller, oldHeaderScroller) {
    if (headerScroller) {
      headerScroller = Scroller.new({
        _id: `${this.id}-header-scroller`,
        element: this.headerCellContainer,
        widget: this
      }, headerScroller);
    } else {
      oldHeaderScroller == null ? void 0 : oldHeaderScroller.destroy();
    }
    return headerScroller;
  }
  /**
   * Brings an event or a time into view.
   * @param {Scheduler.model.EventModel|Date|Number} target The event to scroll to or
   * a `Date` to read the hour value from, or an hour number.
   * @param {BryntumScrollOptions} [options] How to scroll.
   * @returns {Promise} A promise which is resolved when the target has been scrolled into view.
   */
  async scrollTo(target) {
    const me = this, { eventFilter } = me;
    if (target.isEvent && (!eventFilter || eventFilter(target)) && DateHelper.intersectSpans(me.startDate, me.endDate, target.startDate, target.endDate)) {
      if (!me.getEventElement(target)) {
        if (!(me.expanded || me.autoHeight)) {
          me.toggleExpandCollapse();
        }
      }
    }
    return super.scrollTo(...arguments);
  }
  updateDate(date2, oldDate) {
    var _a5;
    const me = this;
    let el = oldDate && me.getDayHeader(oldDate);
    el == null ? void 0 : el.classList.remove("b-selected-date");
    (_a5 = super.updateDate) == null ? void 0 : _a5.call(this, date2, oldDate);
    el = date2 && me.getDayHeader(date2);
    el == null ? void 0 : el.classList.add("b-selected-date");
  }
  getDayHeader(date2) {
    if (typeof date2 !== "string") {
      date2 = this.dateKey(date2);
    }
    return this.headerCellContainer.querySelector(`.b-cal-cell-header[data-header-date="${date2}"]`);
  }
  get overflowElement() {
    return this.bodyElement;
  }
  get maxHeightElement() {
    var _a5;
    return ((_a5 = this.owner) == null ? void 0 : _a5.alldayRowElement) || super.maxHeightElement;
  }
  createCellMap(getEventsOptions = {}) {
    const me = this, hiddenDays = me.hideNonWorkingDays ? me.nonWorkingDays : emptyObject17, { maxEventCount } = me, options = ObjectHelper.assign({
      dayTime: me.dayTime,
      // Only the first *visible* cell needs overflows flowing into it.
      // from after that, propagateCellEvents copies events forward, so
      // the getEvents will use the "startDate" index to extract events for a date.
      getDateIndex: (date2) => Number(date2) === Number(me.firstVisibleDate || me.startDate) ? "date" : "startDate"
    }, getEventsOptions), cellMap = super.createCellMap(options), { owner } = me;
    let newMaxEventCount = 0, lastVisibleCellData;
    cellMap.forEach((cellData) => {
      if (!hiddenDays[cellData.day]) {
        lastVisibleCellData = cellData;
      }
      newMaxEventCount = Math.max(newMaxEventCount, cellData.renderedEvents.length);
    });
    me.maxEventCount = newMaxEventCount;
    if (newMaxEventCount !== maxEventCount) {
      cellMap.clear();
      return me.createCellMap(options);
    }
    lastVisibleCellData && (lastVisibleCellData.isRowEnd = true);
    owner == null ? void 0 : owner.element.classList.toggle("b-has-allday-events", Boolean(cellMap.size));
    return cellMap;
  }
  collectEvents(options) {
    options = ObjectHelper.assign({
      dayTime: this.dayTime
    }, options, {
      // Only the first *visible* cell needs overflows flowing into it.
      // from after that, propagateCellEvents copies events forward, so
      // the getEvents will use the "startDate" index to extract events for a date.
      getDateIndex: (date2) => Number(date2) === Number(this.firstVisibleDate || this.startDate) ? "date" : "startDate"
    });
    return super.collectEvents(options);
  }
  createCellData(date2) {
    const me = this, columnIndex = DateHelper.diff(me.startDate, date2, "day"), visibleColumnIndex = columnIndex;
    return Object.assign(super.createCellData(date2), {
      visibleColumnIndex,
      columnIndex,
      isRowStart: visibleColumnIndex === 0,
      isRowEnd: false,
      // Events can forward-occupy slots if they
      // overrun their start day.
      // So the next step is to propagate forward
      // multi day events into future cells they cover.
      renderedEvents: new EventSlots()
    });
  }
  get dayStartShift() {
    var _a5, _b2;
    return (_b2 = (_a5 = this.dayTime) == null ? void 0 : _a5.startShift) != null ? _b2 : this._dayStartShift;
  }
  updateDayStartShift(dayStartShift) {
    this.element.classList.toggle("b-shifted-day", Boolean(dayStartShift));
    this.dayTime = new DayTime(dayStartShift);
  }
  changeDayTime(dayTime) {
    var _a5;
    if (!dayTime) {
      dayTime = new DayTime(this.dayStartShift);
    }
    if (!dayTime.isDayTime) {
      dayTime = new DayTime(dayTime);
    }
    if (!((_a5 = this._dayTime) == null ? void 0 : _a5.equals(dayTime))) {
      return dayTime;
    }
  }
  dayOfDate(date2) {
    return this.dayTime.dayOfDate(date2);
  }
  updateMinDayWidth(minDayWidth) {
    const { style } = this.element;
    style.setProperty("--min-day-width", minDayWidth ? DomHelper.setLength(minDayWidth) : 0);
    style.setProperty("--cell-container-overflow", minDayWidth ? "visible" : "hidden");
    this.afterUpdateMinDayWidth();
  }
  // Fix up scrollability after minDayWidth update. This happens on AF.
  afterUpdateMinDayWidth() {
    const { headerScroller, minDayWidth, scrollable } = this, { element } = scrollable;
    if (minDayWidth && element.scrollWidth > element.clientWidth) {
      scrollable.overflowX = headerScroller.overflowX = "hidden-scroll";
    } else {
      scrollable.overflowX = headerScroller.overflowX = false;
    }
  }
  updateEventHeight() {
    super.updateEventHeight(...arguments);
    if (!this.autoHeight && !this.isConfiguring && !this.expanded) {
      this.setEventContentHeight(this.cellContentHeight);
    }
  }
  updateGutterHeight() {
    if (!this.isConfiguring) {
      this.setEventContentHeight(this.cellContentHeight);
    }
  }
  updateNonWorkingDays() {
    this.onVisibleDateRangeChange();
  }
  updateHideNonWorkingDays() {
    super.updateHideNonWorkingDays(...arguments);
    this.onVisibleDateRangeChange();
  }
  // Override from DayCellRenderer
  // Called automatically on the CellOverflow${overflowPopupTrigger} event because of callOnFunctions
  onCellOverflowGesture({ date: date2 }) {
    if (expandGestures[this.overflowClickAction.toLowerCase()]) {
      this.expanded = true;
    } else {
      super.onCellOverflowGesture(...arguments);
    }
  }
  onVisibleDateRangeChange() {
    var _a5;
    if (!this.isConfiguring) {
      (_a5 = this._cellMap) == null ? void 0 : _a5.clear();
      this.refresh();
    }
  }
  changeAutoHeight(autoHeight) {
    const me = this, { eventsPerCell } = me;
    me._autoHeight = autoHeight;
    me.setEventContentHeight(me.cellContentHeight);
    if (!me.isConfiguring) {
      me.performResizeRefresh(eventsPerCell, me.eventContainerTop, true);
    }
  }
  async setEventContentHeight(eventContentHeight, syncingSiblings) {
    var _a5, _b2, _c2;
    const me = this, {
      cellContainer: element,
      parent
    } = me;
    if (me._eventContentHeight !== eventContentHeight) {
      if (!syncingSiblings && (parent == null ? void 0 : parent.isDayView) && ((_a5 = parent.parent) == null ? void 0 : _a5.isResourceView)) {
        const siblings = parent.parent.items.map((v) => v.allDayEvents).filter((v) => v && v !== me);
        eventContentHeight = Math.max(eventContentHeight || 0, ...siblings.filter((v) => !v.owner.isResourceDayViewTimeAxis).map((v) => v.calculateCellContentHeight() || 0));
        siblings == null ? void 0 : siblings.forEach((v) => {
          v.setEventContentHeight(eventContentHeight, true);
        });
      }
      const { height: oldHeight } = me;
      let { animate } = me;
      me.isAnimating = animate;
      const duration = DomHelper.getPropertyTransitionDuration(element, "height");
      if (!duration) {
        me.isAnimating = animate = false;
      }
      element.style.height = `${eventContentHeight || 1}px`;
      me.bodyElement.classList.toggle("b-zero-height", !Boolean(eventContentHeight));
      if (animate) {
        await (me.heightAnimation = EventHelper.waitForTransitionEnd({
          property: "height",
          element,
          duration
        }));
      }
      if (!me.isDestroyed) {
        const { height } = me;
        me.isAnimating = false;
        me._eventContentHeight = eventContentHeight;
        (_c2 = (_b2 = me.owner) == null ? void 0 : _b2.syncScrollbarPadding) == null ? void 0 : _c2.call(_b2);
        if (height !== oldHeight) {
          me.trigger("heightChange", {
            height,
            oldHeight
          });
        }
      }
    }
  }
  get hasOverflow() {
    return !this.autoHeight && this.maxEventCount > this.defaultEventRowCount;
  }
  get eventsPerCell() {
    const me = this, { maxEventCount } = me;
    return me.expanded || me.autoHeight ? maxEventCount : Math.min(maxEventCount, me.defaultEventRowCount);
  }
  get cellContentHeight() {
    var _a5;
    return ((_a5 = this.owner) == null ? void 0 : _a5.showAllDayHeader) === false ? 0 : this.calculateCellContentHeight();
  }
  calculateCellContentHeight() {
    const me = this, { gutterHeight } = me;
    let result = Math.ceil(me.eventContainerTop + me.eventsPerCell * (me.eventHeightInPixels + me.eventSpacing));
    if (gutterHeight) {
      if (me.eventsPerCell) {
        result -= me.eventSpacing;
      }
      if (isNaN(gutterHeight)) {
        result = `calc(${result}px + ${gutterHeight})`;
      } else {
        result += Number(gutterHeight);
      }
    }
    return result;
  }
  getDateFromPosition(clientX, clientY, local = false, keyParser) {
    const me = this, {
      eventContentElement
    } = me, rowRect = eventContentElement.getBoundingClientRect(), dx = me.rtl ? rowRect.right - clientX : clientX - rowRect.left, width = eventContentElement.scrollWidth, overCell = me.getDayElementFromX(clientX);
    if (dx < 0 || width < dx || clientY < rowRect.top || rowRect.bottom < clientY) {
      return null;
    }
    return me.getDateFromElement(overCell, keyParser);
  }
  /**
   * If not {@link #config-autoHeight}, this toggles between the collapsed state where it shows
   * the {@link #config-defaultEventRowCount} even if more event rows exist, and the expanded state
   * (which is equivalent to {@link #config-autoHeight}) where all event rows are shown.
   */
  async toggleExpandCollapse() {
    const { expanded } = this;
    await this.updateExpanded(!Boolean(expanded), expanded);
  }
  async updateExpanded(expanded, wasExpanded) {
    const me = this;
    me._expanded = wasExpanded;
    const { eventsPerCell } = me;
    me._expanded = expanded;
    if (me.expanded) {
      if (me.isVisible) {
        me.performResizeRefresh(eventsPerCell, me.eventContainerTop, true);
        await me.heightAnimation;
      }
    } else {
      me.scrollable.overflowY = "hidden";
      await me.setEventContentHeight(me.cellContentHeight);
      if (wasExpanded && !me.isDestroyed) {
        me.performResizeRefresh(eventsPerCell, me.eventContainerTop, true);
      }
    }
  }
  get visibleDateHeaders() {
    const me = this;
    let firstDate, lastDate;
    if (me.fullWeek) {
      firstDate = me.month.getWeekStart(me.month.getWeekNumber(me.startDate));
      while (me.hiddenNonWorkingDays[firstDate.getDay()]) {
        firstDate.setDate(firstDate.getDate() + 1);
      }
      lastDate = DateHelper.add(firstDate, 6, "d");
      while (me.hiddenNonWorkingDays[lastDate.getDay()]) {
        lastDate.setDate(lastDate.getDate() - 1);
      }
    } else {
      firstDate = me.firstVisibleDate;
      lastDate = me.lastVisibleDate;
    }
    return [firstDate, lastDate];
  }
  // We must implement the CalendarMixin interface.
  // All views must expose a doRefresh method.
  doRefresh() {
    var _a5, _b2, _c2;
    (_a5 = this._cellMap) == null ? void 0 : _a5.clear();
    const me = this, {
      dayTime,
      fullWeek,
      startDate,
      endDate,
      headerCellContainer,
      cellContainer,
      dayHeaderRenderer,
      hiddenNonWorkingDays,
      hideEmptyDays,
      dateFilter,
      owner
    } = me, [from, to] = me.visibleDateHeaders, cellMap = (startDate || endDate) && me.cellMap, nonWorkingDays = (_b2 = me.nonWorkingDays) != null ? _b2 : me.month.nonWorkingDays, cellDataBlocks = [], eventCells = [], headerCells = [];
    if (!cellMap) {
      return;
    }
    let columnIndex = 0, visibleColumnIndex = 0, currentDay, i, lastWorkingDayCell;
    for (const date2 = from; !(to < date2); date2.setDate(date2.getDate() + 1), columnIndex++) {
      const cellData = cellMap.get(dayTime.dateKey(date2)) || me.createCellData(date2), { day: day2, key } = cellData, dayEvents = hideEmptyDays && (owner ? owner.getEventsForDay(key, me.startDate) : cellData.renderedEvents), skipDay = hideEmptyDays && !(dayEvents == null ? void 0 : dayEvents.length) || dateFilter && !me.callback(dateFilter, me, [cellData]);
      if (!(hiddenNonWorkingDays[day2] || skipDay)) {
        cellData.columnIndex = cellData.cellIndex = columnIndex++;
        cellData.isRowStart = visibleColumnIndex === 0;
        cellData.visibleColumnIndex = visibleColumnIndex;
        if (!fullWeek) {
          visibleColumnIndex++;
        }
        cellDataBlocks.push(cellData);
      }
    }
    me.weekLength = fullWeek ? 1 : visibleColumnIndex;
    const length = cellDataBlocks.length;
    for (i = 0; i < length; i++) {
      const cellData = cellDataBlocks[i], {
        date: date2,
        day: day2,
        key
      } = cellData, isNonWorking = nonWorkingDays[day2], dayCls = `b-day-of-week-${day2}`;
      currentDay = !(date2 - ((owner == null ? void 0 : owner.date) || me.date));
      cellData.cell = me.getCell(key);
      cellData.row = me.cellContainer;
      const cellDomConfig = me.getCellDomConfig(cellData);
      if (cellDomConfig) {
        const headerDomConfig = Objects.merge({
          dataset: {
            // The [data-date] property marks an event content cell.
            headerDate: key
          }
        }, cellDomConfig.children[0]);
        headerDomConfig.className["b-selected-date"] = currentDay;
        headerDomConfig.className["b-current-date"] = fullWeek && currentDay;
        headerDomConfig.className[dayCls] = 1;
        headerDomConfig["aria-label"] = cellDomConfig["aria-label"];
        dayHeaderRenderer && me.callback(dayHeaderRenderer, me, [headerDomConfig, cellData]);
        cellDomConfig.dataset.date = key;
        Object.assign(cellDomConfig.className, {
          "b-dayview-allday": 1,
          [me.nonWorkingDayCls]: isNonWorking,
          [me.weekendCls]: DateHelper.weekends[day2],
          [me.dayCellCls]: 1,
          [dayCls]: 1
        });
        cellDomConfig.children.shift();
        headerCells.push(headerDomConfig);
        if (!fullWeek || currentDay) {
          eventCells.push(cellDomConfig);
          if (!isNonWorking) {
            lastWorkingDayCell = cellDomConfig;
          }
        }
      }
    }
    lastWorkingDayCell && (lastWorkingDayCell.className["b-last-working-day"] = 1);
    DomSync.sync({
      targetElement: headerCellContainer,
      domConfig: {
        children: headerCells,
        onlyChildren: true,
        // Ensure DOM order matches children order.
        syncOptions: {
          releaseThreshold: 0,
          strict: true
        }
      }
    });
    DomSync.sync({
      targetElement: cellContainer,
      domConfig: {
        children: eventCells,
        onlyChildren: true,
        // Ensure DOM order matches children order.
        syncOptions: {
          releaseThreshold: 0,
          strict: true
        }
      }
    });
    me.refreshCount = (me.refreshCount || 0) + 1;
    me.collapseGutter();
    me.setEventContentHeight(((_c2 = this.owner) == null ? void 0 : _c2.showAllDayHeader) === false ? 0 : me.cellContentHeight);
    me.trigger("refresh");
  }
  get eventContainerTop() {
    return 0;
  }
  async expandGutter() {
    const me = this;
    if (!me._isTemporarilyExpanded) {
      me.gutterHeight = (me.gutterHeight || 0) + me.eventHeightInPixels + me.eventSpacing;
      await me.setEventContentHeight(me.cellContentHeight);
      me._isTemporarilyExpanded = true;
    }
  }
  async collapseGutter() {
    const me = this;
    if (me._isTemporarilyExpanded) {
      me.gutterHeight = me.gutterHeight - (me.eventHeightInPixels + me.eventSpacing);
      await me.setEventContentHeight(me.cellContentHeight);
      me._isTemporarilyExpanded = false;
    }
  }
  onCalendarKeyDown(keyEvent) {
    var _a5, _b2, _c2, _d;
    const {
      dayTime,
      element,
      owner
    } = this, { target } = keyEvent, eventCellSelector = `.${this.dayCellCls}`, from = target.closest(`.b-cal-cell-header,${eventCellSelector}`) || target, headerFocused = from.matches(".b-cal-cell-header"), eventCellFocused = from.matches(eventCellSelector), cellKey = eventCellFocused ? "date" : "header-date", dateKey = this.duration === 1 && keyEvent.key === "ArrowDown" ? dayTime.dateKey(this.date) : from.dataset[eventCellFocused ? "date" : "headerDate"], date2 = dayTime.parseKey(dateKey), nextNodeSelector = headerFocused ? ".b-cal-cell-header" : eventCellSelector;
    let to;
    switch (keyEvent.key) {
      case "ArrowUp":
        if (eventCellFocused) {
          keyEvent.preventDefault();
          (_a5 = element.querySelector(`.b-cal-cell-header[data-header-date="${dateKey}"]`)) == null ? void 0 : _a5.focus();
        }
        break;
      case "ArrowDown":
        if (headerFocused && DomHelper.isInView(to = element.querySelector(`${eventCellSelector}[data-date="${dateKey}"]`))) {
          keyEvent.preventDefault();
          to == null ? void 0 : to.focus();
        } else if (owner.isDayView) {
          keyEvent.preventDefault();
          (_b2 = owner.eventContentElement.querySelector(`.${owner.dayCellCls}[data-date="${dateKey}"]`)) == null ? void 0 : _b2.focus();
        }
        break;
      case "ArrowLeft":
        keyEvent.preventDefault();
        (_c2 = element.querySelector(`${nextNodeSelector}[data-${cellKey}="${dayTime.dateKey(DateHelper.add(date2, -1, "d"))}"]`)) == null ? void 0 : _c2.focus();
        break;
      case "ArrowRight":
        keyEvent.preventDefault();
        (_d = element.querySelector(`${nextNodeSelector}[data-${cellKey}="${dayTime.dateKey(DateHelper.add(date2, 1, "d"))}"]`)) == null ? void 0 : _d.focus();
        break;
      default:
        return super.onCalendarKeyDown(keyEvent);
    }
  }
  // Override from CalendarMixin.
  // CalendarRow's "today" may be shifted by the dayStartShift.
  // So when dayStartShift is 18, days start at 6pm, so today is 6pm today.
  // If we are the all day row of a DayView, use the owning DayView's isToday.
  isToday(date2) {
    const { owner } = this;
    return (owner == null ? void 0 : owner.isDayView) ? owner.isToday(date2) : DateHelper.isToday(date2, this.timeZone);
  }
  // addCellHeaderContent mutates the cellHeader DomConfig block.
  // And if we are to have a day name element, returns the DomConfig for it.
  // It's called from DayCellRenderer#getCellDomConfig
  addCellHeaderContent(cellHeader, cellData) {
    var _a5;
    const me = this, { resource } = me, { date: date2, day: day2, tomorrow } = cellData, todayText = me.isToday(date2) ? me.L("L{Object.today}, ") : "", { dayNameFormat, dayNumberCompress, daySeparator } = me, shifted = (_a5 = me == null ? void 0 : me.dayTime) == null ? void 0 : _a5.startShift;
    cellHeader.children = [{
      className: `b-day-name-part b-day-name-day${dayNumberCompress ? " b-day-name-short" : ""}`,
      html: formatDayPart(dayNameFormat, daySeparator[0], date2, shifted && tomorrow)
    }, {
      tabIndex: 0,
      role: "link",
      className: `b-day-name-part b-day-name-date`,
      "aria-label": `${todayText}${DateHelper.format(date2, "dddd Do MMMM")}${resource ? ` ${resource.name}` : ""}.`,
      html: formatDayPart(me.dayNumberFormat, daySeparator[1], date2, shifted && tomorrow, dayNumberCompress)
    }];
    cellHeader.className[me.nonWorkingDayCls] = cellData.isNonWorking;
    cellHeader.className[me.weekendCls] = DateHelper.weekends[day2];
  }
  updateLocalization() {
    if (!this.isConfiguring) {
      this.refresh();
    }
    super.updateLocalization();
  }
  updateFullWeek() {
    if (!this.isConfiguring) {
      this.refresh();
    }
  }
};
__publicField(CalendarRow, "$name", "CalendarRow");
// Factoryable type name
__publicField(CalendarRow, "type", "calendarrow");
__publicField(CalendarRow, "delayable", {
  afterUpdateMinDayWidth: {
    type: "raf",
    cancelOutstanding: true
  }
});
CalendarRow.initClass();
CalendarRow._$name = "CalendarRow";

// lib/Calendar/widget/DaySelector.js
var DaySelector = class extends CalendarRow {
  afterConfigure() {
    var _a5;
    const me = this, { owner } = me;
    super.afterConfigure();
    (_a5 = owner.scrollable) == null ? void 0 : _a5.ion({
      overflowChange: "onOwnerOverflowChange",
      thisObj: me
    });
    owner.descriptionRenderer = me.descriptionRenderer.bind(me);
    FunctionHelper.before(owner, "updateDate", this.beforeOwnerDateUpdate, this);
  }
  beforeOwnerDateUpdate(date2) {
    this.isShifting = true;
    this.date = date2;
    this.isShifting = false;
  }
  onOwnerOverflowChange({ y }) {
    this.headerElement.classList.toggle("b-show-yscroll-padding", Boolean(y));
  }
  onDayNumberClick({ date: date2 }) {
    if (this.owner) {
      this.owner.date = date2;
    }
    return false;
  }
  scrollTo() {
  }
  // We don't scroll
  descriptionRenderer() {
    const {
      startDate,
      endDate,
      owner
    } = this, startMonth = startDate.getMonth(), endMonth = endDate.getMonth(), monthDesc = startMonth === endMonth ? DateHelper.format(startDate, "MMMM") : `${DateHelper.format(startDate, "MMM")} - ${DateHelper.format(endDate, "MMM")}`, week = owner.month.getWeekNumber(startDate);
    return `${this.L("L{Object.Week}")} ${week[1]}, ${monthDesc} ${week[0]}`;
  }
};
__publicField(DaySelector, "$name", "DaySelector");
// Factoryable type name
__publicField(DaySelector, "type", "dayselector");
__publicField(DaySelector, "configurable", {
  readOnly: true,
  autoCreate: false,
  flex: "0 0 auto",
  dock: "top"
});
DaySelector.initClass();
DaySelector._$name = "DaySelector";

// lib/Calendar/widget/mixin/DaySelectable.js
var DaySelectable_default = (Target) => {
  var _a5;
  return _a5 = class extends (Target || Base) {
    static get configurable() {
      return {
        /**
         * If {@link #config-daySelector} is set, then this will be an instance
         * of {@link Calendar.widget.DaySelector} which encapsulates the day selector at the top
         * of this view.
         * @member {Calendar.widget.DaySelector} daySelector
         * @readonly
         * @internal
         */
        /**
         * A Configure this as `true` to show a {@link Calendar.widget.DaySelector} docked at the top to
         * act as a day selector. By default, the DaySelector will be seven days in duration and locked
         * to start a the `weekStartDay`
         * @config {Boolean|DaySelectorConfig}
         * @internal
         */
        daySelector: null
      };
    }
    changeDaySelector(daySelector, oldDaySelector) {
      const me = this;
      if (daySelector) {
        me.range = "1 day";
        me.settings = null;
        me.hideHeaders = true;
        const startDate = me.dayTime.shiftDate(me.month.getWeekStart(me.month.getWeekNumber(me.startDate))), result = DaySelector.reconfigure(oldDaySelector, daySelector, {
          owner: me,
          defaults: {
            type: "dayselector",
            calendar: me.calendar,
            parent: me,
            cls: {
              [`b-${me.type}-dayselector`]: 1
            },
            dayStartShift: me.dayStartShift,
            dayTime: me.dayTime,
            month: me.month,
            date: me.date,
            startDate,
            range: { magnitude: 1, unit: "week" },
            shiftIncrement: { unit: "d", magnitude: me.duration },
            hideNonWorkingDays: me.hideNonWorkingDays,
            nonWorkingDays: me.nonWorkingDays,
            nonWorkingDayCls: me.nonWorkingDayCls,
            weekendCls: me.weekendCls,
            weekStartDay: me.weekStartDay
          }
        });
        me.element.classList.add(`b-${me.type}-with-dayselector`);
        me.tbar = result;
        return result;
      } else {
        oldDaySelector == null ? void 0 : oldDaySelector.destroy();
      }
    }
    //region Current config
    getCurrentConfig(options) {
      const result = super.getCurrentConfig(options);
      delete result.daySelector;
      return result;
    }
    //endregion
  }, __publicField(_a5, "$name", "CalendarMixin"), _a5;
};

// lib/Calendar/widget/RangeMenu.js
var hasEventStore2 = (w) => w == null ? void 0 : w.eventStore;
var RangeMenu = class extends Menu {
  construct() {
    super.construct(...arguments);
    this.ion({
      beforeShow: "syncRangeOptions",
      toggle: "syncViewRange",
      thisObj: this
    });
  }
  syncViewRange(e) {
    if (e.checked) {
      const { view } = this, { date: date2 } = view;
      if (!view.range) {
        view._date = view._endDate = null;
      }
      view.setConfig({
        range: e.item.range,
        date: date2
      });
      e.bubbles = false;
    }
  }
  syncRangeOptions() {
    const { view } = this, { range } = view;
    if (range) {
      const { unit, magnitude } = range;
      for (const menuItem of Object.values(this.items)) {
        if (menuItem) {
          const itemConfig = {};
          if (menuItem._unit === unit) {
            itemConfig.checked = true;
            itemConfig.range = { unit, magnitude };
            itemConfig.text = magnitude === 1 ? StringHelper.capitalize(DateHelper.getLocalizedNameOfUnit(unit)) : DateHelper.formatDelta(itemConfig.range);
          } else {
            itemConfig.checked = false;
            itemConfig.text = StringHelper.capitalize(DateHelper.getLocalizedNameOfUnit(menuItem._unit));
            itemConfig.range = menuItem._unit;
          }
          menuItem.setConfig(itemConfig);
        }
      }
    }
  }
  changeItems(items2) {
    const me = this, view = me.view || (me.view = me.up(hasEventStore2)), toggleGroup = `${view.id}-range-items`;
    Object.values(items2).forEach((item) => {
      item && (item.toggleGroup = toggleGroup);
    });
    return super.changeItems(items2);
  }
  updateView(view) {
    view == null ? void 0 : view.scrollable.ion({
      overflowChange: () => {
        var _a5;
        return (_a5 = this.realign) == null ? void 0 : _a5.call(this);
      }
    });
  }
};
__publicField(RangeMenu, "$name", "RangeMenu");
__publicField(RangeMenu, "type", "rangemenu");
__publicField(RangeMenu, "configurable", {
  autoShow: false,
  view: null,
  floating: false,
  positioned: true,
  anchor: true,
  defaults: {
    checked: false
  },
  items: {
    listRangeDayItem: {
      range: "day",
      text: "L{EventEdit.day}",
      _unit: "day"
    },
    listRangeWeekItem: {
      range: "week",
      text: "L{EventEdit.week}",
      _unit: "week"
    },
    listRangeMonthItem: {
      range: "month",
      text: "L{EventEdit.month}",
      _unit: "month"
    },
    listRangeYearItem: {
      range: "year",
      text: "L{EventEdit.year}",
      _unit: "year"
    },
    listRangeDecadeItem: {
      range: "decade",
      text: "L{EventEdit.decade}",
      _unit: "decade"
    }
  }
});
RangeMenu.initClass();
RangeMenu._$name = "RangeMenu";

// lib/Calendar/widget/EventList.js
var byStartDate = (lhs, rhs) => lhs.startDate.valueOf() - rhs.startDate.valueOf();
var mergeColumns = (configuredColumns, defaultColumns) => {
  if (defaultColumns) {
    configuredColumns = configuredColumns.data || configuredColumns;
    if (ObjectHelper.isObject(configuredColumns)) {
      const result = defaultColumns.slice();
      for (const id2 in configuredColumns) {
        const newColumn = configuredColumns[id2], matchIndex = result.findIndex((c) => c.id === id2 || c.field === id2);
        if (newColumn) {
          const v = Config.merge(newColumn, result[matchIndex]);
          v.id = id2;
          result[matchIndex === -1 ? result.length : matchIndex] = v;
        } else if (matchIndex > -1) {
          result.splice(matchIndex, 1);
        }
      }
      return result;
    } else {
      defaultColumns = new Collection({
        values: defaultColumns,
        idProperty: "field"
      });
      for (let i = 0, { length } = configuredColumns; i < length; i++) {
        const newColumn = configuredColumns[i], matchedExistingColumn = defaultColumns.get(newColumn.field);
        if (matchedExistingColumn) {
          configuredColumns[i] = Config.merge(newColumn, matchedExistingColumn);
          defaultColumns.remove(matchedExistingColumn);
        }
      }
      configuredColumns.push(...defaultColumns.values);
      return configuredColumns;
    }
  } else {
    return configuredColumns;
  }
};
var cleanseRowElement = (e) => {
  delete e.dataset.eventId;
  delete e.dataset.date;
};
var EventList = class extends Grid.mixin(CalendarMixin_default, DayCellCollecter_default, DaySelectable_default, DateRangeOwner_default) {
  construct(...args) {
    const me = this, featuresProp = ObjectHelper.getPropertyDescriptor(this, "features");
    Object.defineProperty(me, "features", {
      get: () => {
        return featuresProp.get.call(this) || {};
      },
      set: (features) => {
        var _a5;
        if (features === true) {
          features = {};
        }
        if (!features.print) {
          features.print = Boolean((_a5 = this.calendar) == null ? void 0 : _a5.features.print);
        }
        featuresProp.set.call(this, features);
      },
      configurable: true
    });
    super.construct(...args);
  }
  // We must implement the CalendarMixin interface.
  // All views must expose a doRefresh method.
  doRefresh() {
    this.refreshRows();
  }
  getEventElement(eventRecord, date2 = Math.max(eventRecord.startDate, this.firstVisibleDate || this.startDate)) {
    return this.eventContentElement.querySelector(`.b-grid-row[data-event-id="${eventRecord.id}"][data-date="${DateHelper.makeKey(date2)}"]`) || super.getEventElement(...arguments);
  }
  // Returns true is this EventList encapsulates this date
  containsDate(date2) {
    const dateContainmentFn = this.range ? "betweenLesser" : "betweenLesserEqual";
    return DateHelper[dateContainmentFn](date2, this.startDate, this.endDate);
  }
  /**
   * This method produces the event time details next to the event bar in
   * {@link Calendar.widget.EventList} and {@link Calendar.widget.AgendaView}
   * as a {@link DomConfig} object.
   *
   * The content is encapsulated in an element with CSS class `b-cal-eventlist-event-time`.
   * For multi day events, the ending date is shown. For intra-day events, the start and end
   * times are shown.
   * @param {Scheduler.model.EventModel} eventRecord
   * @param {Date} forDate The date cell into which the event is being rendered.
   * @returns {DomConfig}
   */
  eventListEventTimeRenderer(eventRecord, forDate) {
    const me = this;
    let timeContent;
    if (me.eventTimeRenderer) {
      timeContent = me.callback(me.eventTimeRenderer, me, arguments);
    } else {
      timeContent = me.defaultEventTimeRenderer(eventRecord, forDate);
    }
    const prop = typeof timeContent === "string" ? "text" : "children";
    return {
      className: {
        "b-cal-eventlist-event-time": 1
      },
      "aria-hidden": true,
      [prop]: prop === "text" ? timeContent : ArrayHelper.asArray(timeContent)
    };
  }
  /**
   * Produces the time details next to the event bar in {@link Calendar.widget.EventList} and {@link Calendar.widget.AgendaView}.
   *
   * This may be called from a custom {@link #property-eventTimeRenderer} function to produce
   * the default time details.
   * @param {Scheduler.model.EventModel} eventRecord
   * @param {Date} forDate The date cell into which the event is being rendered.
   * @returns {String} The time details.
   */
  defaultEventTimeRenderer(eventRecord, forDate) {
    const me = this, {
      startDate,
      endDate,
      allDay
    } = eventRecord, startsOn = DateHelper.clearTime(startDate), endsOn = DateHelper.clearTime(endDate), endingDate = endsOn && (endDate - endsOn ? endDate : DateHelper.add(endsOn, -1, "d")), crossesDay = !eventRecord.isMilestone && (endDate - endsOn ? endsOn : endingDate) - startsOn;
    let timeString;
    if (crossesDay) {
      if (!allDay && !(forDate - startsOn)) {
        timeString = `${DateHelper.format(startDate, me.eventListTimeFormat)} - ${DateHelper.format(endingDate, "DD MMM")}`;
      } else if (!allDay && !(forDate - endsOn)) {
        timeString = me.L("L{Calendar.endsOn}", DateHelper.format(endDate, me.eventListTimeFormat));
      } else {
        timeString = me.L("L{Calendar.endsOn}", DateHelper.format(endingDate, "DD MMM"));
      }
    } else {
      timeString = allDay ? me.L("L{Calendar.allDay}") : `${DateHelper.format(eventRecord.startDate, me.eventListTimeFormat)} - ${DateHelper.format(endDate, me.eventListTimeFormat)}`;
    }
    return timeString;
  }
  /**
   * This method produces the date details block in {@link Calendar.widget.AgendaView} cells
   * as a {@link DomConfig} object.
   *
   * The content is encapsulated in an element with CSS class `b-cal-agenda-date`.
   * @param {Date} date
   * @returns {DomConfig}
   */
  agendaEventDateRenderer(date2) {
    return {
      className: {
        "b-day-name": 1,
        "b-cal-agenda-date": 1,
        "b-today": !Boolean(DateHelper.clearTime(/* @__PURE__ */ new Date()) - DateHelper.clearTime(date2))
      },
      "aria-hidden": true,
      children: [{
        className: {
          "b-cal-agenda-date-date-number": 1
        },
        text: date2.getDate()
      }, {
        className: {
          "b-cal-agenda-date-date-text": 1
        },
        children: [{
          text: DateHelper.format(date2, "dddd")
        }, {
          text: DateHelper.format(date2, "MMM YYYY")
        }]
      }]
    };
  }
  // Override because our Featurable mixin which comes before GridBase sets features to null
  // to destroy them, so we must ensure the inherited Grid features are cleaned up.
  destroy() {
    var _a5;
    for (const feature of Object.values(this.features)) {
      (_a5 = feature.destroy) == null ? void 0 : _a5.call(feature);
    }
    super.destroy();
  }
  populateHeaderMenu({ items: items2 }) {
    const { listRangeMenu: menu } = this;
    if (menu) {
      menu.appendTo = this.element;
      items2.listRangeItem = {
        weight: -1e3,
        icon: "b-icon-calendar-week",
        text: "L{Calendar.timeRange}",
        menu
      };
    }
    super.populateHeaderMenu(...arguments);
  }
  refreshVirtualScrollbars() {
    super.refreshVirtualScrollbars();
    this.bodyElement.classList[DomHelper.scrollBarWidth && this.hasVerticalOverflow ? "add" : "remove"]("b-has-scrollbar");
  }
  /**
   * This property yields a {@link Calendar.widget.RangeMenu} configuration block which encapsulates the range choices
   * which this widget may be set to encapsulate:
   * - day
   * - week
   * - month
   * - year
   * - decade
   *
   * By default a `list` view adds these choices to the header context menu.
   * An `agenda` view creates a floating settings button which offers this menu.
   * The property may be used to create a custom UI for changing the range.
   * The value yielded by the default `get listRangeMenu()` implementation looks like this:
   *
   * ```javascript
   * {
   *     items : {
   *         listRangeDayItem    : {config for DAY range menu item },
   *         listRangWeekItem    : {config for WEEK range menu item },
   *         listRangMonthItem   : {config for MONTH range menu item },
   *         listRangeYearItem   : {config for YEAR range menu item },
   *         listRangeDecadeItem : {config for DECADE range menu item }
   *     }
   * }
   * ```
   *
   * A subclass can override `get listRangeMenu()` and mutate the object returned by the `super` call.
   *
   * For example, it could `delete` properties of the `items` block to limit which ranges may be selected.
   *
   * @member {RangeMenuConfig} listRangeMenu
   * @readonly
   */
  changeListRangeMenu(listRangeMenu) {
    if (listRangeMenu) {
      return RangeMenu.mergeConfigs({
        view: this,
        floating: false
      }, listRangeMenu);
    }
  }
  updateRowManager(rowManager) {
    var _a5;
    (_a5 = super.updateRowManager) == null ? void 0 : _a5.call(this, ...arguments);
    rowManager.ion({
      beforeRenderRow: "onBeforeRenderRow",
      thisObj: this
    });
  }
  onBeforeRenderRow({ row, record }) {
    var _a5, _b2, _c2, _d;
    const { _elementsArray } = row, { id: id2, startDate } = record, oldDate = DateHelper.parseKey(((_b2 = (_a5 = _elementsArray[0]) == null ? void 0 : _a5.dataset) == null ? void 0 : _b2.date) || "");
    if (oldDate) {
      row.cls[`b-day-of-week-${oldDate.getDay()}`] = 0;
    }
    row.eachElement((e) => cleanseRowElement);
    if (record.isEventModel) {
      for (let i = 0, { length } = _elementsArray; i < length; i++) {
        _elementsArray[i].dataset.eventId = id2;
        _elementsArray[i].dataset.date = startDate ? DateHelper.makeKey(startDate) : "";
      }
      startDate && (row.cls[`b-day-of-week-${startDate.getDay()}`] = 1);
      (_d = (_c2 = this.calendar) == null ? void 0 : _c2.beforeListRenderRow) == null ? void 0 : _d.call(_c2, { row, record });
    }
  }
  // Scheduler interface usually routes this to handleEvent.
  // If we're a Grid, GridElementEvents routes events to handleEvent, so override it to do nothing.
  // Otherwise keydown events would be handled twice
  onInternalKeyDown(domEvent) {
  }
  get displayName() {
    let s = this.title;
    if (typeof s !== "string") {
      s = this.type;
    }
    return StringHelper.capitalize(s);
  }
  /**
   * Scrolls vertically to bring an event or a time into view.
   * @param {Scheduler.model.EventModel|Date|String} target The event to scroll to or a `Date`, or `YYYY-MM-DD` date to scroll to.
   * @param {Object} [options] How to scroll.
   * @param {String} [options.block] How far to scroll the target: `start/end/center/nearest`.
   * @param {Number} [options.edgeOffset] edgeOffset A margin around the target to bring into view.
   * @param {Object|Boolean|Number} [options.animate] Set to `true` to animate the scroll by 300ms,
   * or the number of milliseconds to animate over, or an animation config object.
   * @param {Number} [options.animate.duration] The number of milliseconds to animate over.
   * @param {String} [options.animate.easing] The name of an easing function.
   * @param {Boolean|Function} [options.highlight] Set to `true` to highlight the resulting element
   * when it is in view. May be a function which is called passing the resulting element
   * to provide customized highlighting.
   * @param {Boolean} [options.focus] Set to `true` to focus the element when it is in view.
   * @param {Boolean} [options.y] Pass as `false` to disable scrolling in the `Y` axis.
   * @returns {Promise} A promise which is resolved when the target has been scrolled into view.
   */
  async scrollTo(target, options) {
    const me = this;
    options = ObjectHelper.assign({}, me.defaultScrollOptions, typeof options == "boolean" ? { animate: options } : options);
    if (target instanceof me.store.modelClass) {
      await me.scrollRowIntoView(target, Object.assign({}, options, { highlight: false }));
      if (options.highlight) {
        target = me.getEventElement(target, target.startDate);
        if (typeof options.highlight === "boolean") {
          DomHelper.highlight(target, me);
        } else {
          (me.widget || me).callback(options.highlight, null, [target]);
        }
      }
    } else if (ObjectHelper.isDate(target = me.ingestDate(target)) && me.store.count) {
      const key = DateHelper.makeKey(target = me.findClosestOccupiedDate(target));
      me.scrollingToDate = true;
      await me.scrollRowIntoView(me.dateIndex[key], options);
      me.scrollingToDate = false;
      me.scrolledToDate = target;
    }
  }
  // Find the date of the record closest to the passed date
  findClosestOccupiedDate(date2) {
    const me = this, { dayTime } = me, index = me.dateIndex;
    if (!Object.keys(index).length || index[dayTime.dateKey(date2)]) {
      return date2;
    }
    let closest = Number.MAX_SAFE_INTEGER;
    const keys = Object.keys(index);
    for (let i = 0, { length } = keys; i < length; i++) {
      const d = dayTime.parseKey(keys[i]);
      if (Math.abs(d - date2) < Math.abs(closest - date2)) {
        closest = d;
      }
    }
    return new Date(closest);
  }
  updateStartDate() {
    const { refreshCount } = this;
    this.preventScroll = true;
    super.updateStartDate(...arguments);
    this.preventScroll = false;
    if (!this.isConfiguring && this.refreshCount === refreshCount) {
      this.populateStoreSoon();
    }
  }
  updateEndDate() {
    var _a5;
    const { refreshCount } = this;
    (_a5 = super.updateEndDate) == null ? void 0 : _a5.call(this, ...arguments);
    if (!this.isConfiguring && this.refreshCount === refreshCount) {
      this.populateStoreSoon();
    }
  }
  // Interface method used by the Describable Scheduler mixin which renders the date(s) of a view
  // If the result is an array, it creates a range description based on the descriptionFormat.
  get dateBounds() {
    return [this.startDate, this.endDate];
  }
  // We default the range description format to using the shortDateFormat each side (from and to)
  // but this can be configured.
  get descriptionFormat() {
    return super.descriptionFormat || [this.shortDateFormat, `S{${this.shortDateFormat}} - E{${this.shortDateFormat}}`];
  }
  set descriptionFormat(descriptionFormat) {
    super.descriptionFormat = descriptionFormat;
  }
  descriptionRenderer() {
    return `${this.formattedDescription}. ${this.L("L{eventCount}", this.eventCount)}`;
  }
  /**
   * The number of events that this View currently encapsulates in its {@link #config-range date range}.
   * @deprecated Use {@link #property-eventCount} instead.
   * @member {Number}
   * @readonly
   */
  get count() {
    VersionHelper.deprecate("calendar", "7.0.0", "count property is deprecated in favor of eventCount");
    return this.eventCount;
  }
  // Overridden from CalendarMixin which uses the cellMap to ascertain event load
  get eventCount() {
    return this.store.getAllDataRecords().length;
  }
  get store() {
    return super.store;
  }
  set store(store) {
    if (store && !store.modelClass && this.project) {
      store.modelClass = this.project.eventStore.modelClass;
    }
    super.store = store;
  }
  /**
   * When an EventStore arrives, chain off a slave store from that which we can then
   * filter to only show the week we are focused upon.
   */
  updateEventStore(eventStore) {
    var _a5;
    (_a5 = super.updateEventStore) == null ? void 0 : _a5.call(this, eventStore);
    this.detachListeners("eventStore");
    eventStore.ion({
      name: "eventStore",
      refresh: "onCalendarStoreChange",
      thisObj: this
    });
    this.populateStoreSoon();
  }
  changeStore(store) {
    const me = this;
    if (store) {
      store = Store.mergeConfigs({
        useRawData: {
          ignoreDefaults: true,
          disableDefaultValue: true,
          disableTypeConversion: true,
          enabled: true
        },
        modelClass: me.modelClass
      }, store);
    }
    store = super.changeStore(store);
    if (store == null ? void 0 : store.modelClass.isEventModel) {
      me.nonWorkingDaysFilter = store.addFilter({
        id: `${me.id}-nonworkingday-filter`,
        filterBy: (event) => !me.dayTime.isIntraDay(event) || !me.hiddenNonWorkingDays[event.startDate.getDay()],
        disabled: !me.hideNonWorkingDays
      }, true);
    }
    return store;
  }
  updateHideNonWorkingDays(hideNonWorkingDays) {
    const me = this, { store } = me;
    super.updateHideNonWorkingDays(hideNonWorkingDays);
    if (store) {
      me.nonWorkingDaysFilter.disabled = !hideNonWorkingDays;
      store.filter();
    }
    me.trigger("refresh");
  }
  afterRemove({ records }) {
    this.eventStore.remove(records);
    super.afterRemove(...arguments);
  }
  get modelClass() {
    return this.eventStore.modelClass;
  }
  /**
   * This is called when our store needs to be repopulated from the eventStore
   * @private
   */
  populateStoreSoon() {
    const me = this, { project } = me;
    if (me.isVisible) {
      if (project.isInitialCommitPerformed && !project.isWritingData || !project.eventStore.count) {
        me.populateStore();
      }
    } else {
      me.whenVisible("populateStore");
    }
  }
  get duration() {
    return super.duration + (this.range ? 0 : 1);
  }
  populateStore() {
    var _a5;
    const me = this, { cellEdit } = me.features, ec = cellEdit == null ? void 0 : cellEdit.editorContext, events = me.eventStore.getEvents({
      startDate: me.startDate,
      // If we are being set a startDate and endDate, as opposed to using a fixed "range"
      // around a date, then to provide a more intuitive interface, we *include* the endDate
      // for EventLists
      endDate: DateHelper.add(me.endDate, me.range ? 0 : 1, "d"),
      filter: me.eventFilter
    }).sort(byStartDate);
    me.dateIndex = events.reduce((result, event) => {
      const key = DateHelper.makeKey(event.startDate);
      if (!result[key]) {
        result[key] = event;
      }
      return result;
    }, {});
    if (ec) {
      cellEdit.cancelEditing();
    }
    me.store.data = events;
    me.trigger("refresh");
    if (ec) {
      cellEdit.startEditing(ec);
    }
    (_a5 = me.calendar) == null ? void 0 : _a5.syncUIWithActiveView(this);
  }
  /**
   * Creates the Month utility object. We use it to track what week we are looking at.
   */
  changeMonth() {
    const result = super.changeMonth(...arguments);
    result.ion({
      dateChange: "onMonthChange",
      thisObj: this
    });
    return result;
  }
  /**
   * When the date we have been told to look at changes, recalculate the date range.
   * If the date range changes, this will cause a refill of our grid store from the
   * master event store.
   */
  onMonthChange({ newDate }) {
    const me = this, { startDate } = me, newStartDate = me.changeStartDate(newDate);
    if (!startDate || newStartDate - startDate && !me.containsDate(newStartDate)) {
      me.updateRange(me.range);
    }
    if (!me.preventScroll && me.isVisible && me.store.count) {
      me.populateStoreSoon.flush();
      me.scrollTo(newDate);
    }
  }
  /**
   * This is added as a listener by the CalendarStores mixin.
   *
   * Our store is chained off of the EventStore; refill it if the EventStore changes.
   * The store will fire events which cause UI update.
   * We must fire a refresh event so that the encapsulating Calendar view knows about this.
   */
  onCalendarStoreChange({ source: eventStore }) {
    if (!this.date) {
      this.date = eventStore.map((r) => r.startDate).sort((lhs, rhs) => lhs.valueOf() - rhs.valueOf())[0];
    }
    this.populateStoreSoon();
  }
  changeSettingsButton(settings, oldSettings) {
    return settings && Button.reconfigure(oldSettings, Button.mergeConfigs({
      type: "button",
      positioned: true,
      icon: "b-icon-cog",
      menuIcon: null,
      cls: "b-blue b-cal-widget-settings-button",
      insertBefore: this.bodyWrapElement
    }, settings), this);
  }
  //region Current config
  getCurrentConfig(options) {
    const result = super.getCurrentConfig(options);
    delete result.store;
    delete result.data;
    return result;
  }
  //endregion
};
__publicField(EventList, "$name", "EventList");
__publicField(EventList, "type", "eventlist");
__publicField(EventList, "configurable", {
  preserveScrollOnDatasetChange: true,
  cls: "b-slide-vertical",
  eventFilter: null,
  localizableProperties: ["title", "text"],
  title: "L{List}",
  range: "1 month",
  // We have a Month utility object.
  // It helps us with week values and week change events
  month: true,
  /**
   * Column definitions to add to the default columns, or column definitions which override
   * the provided, default columns.
   *
   * By default, the `EventList` shows three columns:
   *
   * ```javascript
   * columns : [{
   *     text  : 'Name',
   *     field : 'name'
   * }, {
   *     text  : 'Start',
   *     field : 'startDate'
   * }, {
   *     text  : 'End',
   *     field : 'endDate'
   * }]
   * ```
   *
   * Your `columns` config is appended to the default columns *unless you provide
   * columns for the `name`, `startDate` and `endDate` fields*, in which case your
   * column definitions are merged into the default column definitions.
   *
   * In this way you can provide a `renderer` for the default columns, or otherwise
   * configure them in a customizable way.
   *
   * You can also supply columns keyed by field, to reconfigure / remove / add similar to how you would manage
   * items in containers and menus:
   *
   * ```javascript
   * columns : {
   *   // Remove the name column
   *   name : null,
   *   // Change the format of the startDate column
   *   startDate : {
   *      format : 'YYYY'
   *   },
   *   // Add a description column
   *   description : {
   *      renderer : ({ record }) => record.description.substring(0, 10)
   *   }
   * }
   * ```
   *
   * @config {Object<String,GridColumnConfig>|GridColumnConfig[]}
   */
  columns: {
    $config: {
      // Columns with matching field names are merged.
      // Non-matching columns are added
      merge: mergeColumns
    },
    value: [{
      text: "L{EventEdit.Name}",
      field: "name",
      flex: "1 0 12em"
    }, {
      text: "L{EventList.Start}",
      //type       : 'startDate', // When column is hoisted from Gantt to Scheduler
      type: "date",
      editor: {
        type: "datetime",
        dateField: {
          step: null
        },
        timeField: {
          step: null
        }
      },
      field: "startDate",
      width: "16.5em",
      format: "L LT",
      renderer({ record, value: value2 }) {
        return record.allDay ? DateHelper.format(value2, "L") : this.defaultRenderer(...arguments);
      }
    }, {
      text: "L{EventList.Finish}",
      //type       : 'endDate', // When column is hoisted from Gantt to Scheduler
      type: "date",
      editor: {
        type: "datetime",
        dateField: {
          step: null
        },
        timeField: {
          step: null
        }
      },
      field: "endDate",
      width: "16.5em",
      format: "L LT",
      renderer({ record, value: value2 }) {
        return record.allDay ? DateHelper.format(value2, "L") : this.defaultRenderer(...arguments);
      }
    }, {
      type: "widget",
      text: "Resources",
      field: "resources",
      minWidth: 250,
      autoHeight: true,
      widgets: [{
        type: "chipview",
        valueProperty: "items",
        displayField: "name",
        itemsFocusable: false,
        navigator: null,
        closable: false,
        style: {
          flexFlow: "row nowrap",
          display: "flex",
          padding: "5px 0 3px 0"
        },
        scrollable: {
          overflowX: "hidden-scroll",
          overflowY: false
        },
        getItemCls: (r) => DomHelper.isNamedColor(r.eventColor) ? ` b-sch-${r.eventColor}` : "",
        getItemStyle: (r) => `color:#fff;${r.eventColor && !DomHelper.isNamedColor(r.eventColor) ? `background-color:${r.eventColor}` : ""}`
      }],
      editor: false
    }]
  },
  features: {
    group: false,
    rowCopyPaste: false,
    columnPicker: {
      createColumnsFromModel: true
    }
  },
  internalListeners: {
    beforeCellEditStart({ editorContext }) {
      var _a5, _b2, _c2, _d;
      const { editor, record } = editorContext;
      if (record.isRecurring || record.isOccurrence) {
        Toast.show({
          html: "Please use EventEdit to edit recurring events"
        });
        return false;
      }
      (_c2 = (_b2 = (_a5 = this.calendar) == null ? void 0 : _a5.features.eventTooltip) == null ? void 0 : _b2._tooltip) == null ? void 0 : _c2.hide();
      (_d = editor.timeField) == null ? void 0 : _d[record.allDay ? "hide" : "show"]();
    }
  },
  settingsButton: {
    $config: "lazy",
    value: null
  },
  /**
   * A {@link Calendar.widget.RangeMenu} configuration block which configures the range choosing menu provided which
   * by default selects one of the following:
   * - day
   * - week
   * - month
   * - year
   * - decade
   *
   * This may be used to either reconfigure that menu, or, by configuring it as `null`, could remove
   * the menu entirely if the date range of this view is controlled by other means.
   *
   * The individual range menu items are under the `items` property and have the following
   * property names:
   * - `listRangeDayItem`
   * - `listRangeWeekItem`
   * - `listRangeMonthItem`
   * - `listRangeYearItem`
   * - `listRangeDecadeItem`
   *
   * These may be reconfigured:
   *
   * ```javascript
   * calendar = new Calendar({
   *     ...
   *     modes : {
   *         agenda : {
   *             listRangeMenu : {
   *                 items : {
   *                     // We don't want the decade range option
   *                     listRangeDecadeItem : null
   *                 }
   *             }
   *         }
   *     }
   * });
   * ```
   *
   * @config {RangeMenuConfig|null} listRangeMenu
   */
  listRangeMenu: {
    $config: "lazy",
    value: {
      type: "rangemenu"
    }
  },
  /**
   * A {@link Core.helper.DateHelper} format string used to format the time displayed
   * next to event bars in the custom columns which {@link Calendar.widget.EventList}
   * based views use - {@link Calendar.widget.EventList} and {@link Calendar.widget.AgendaView}.
   * @config {String}
   * @default
   */
  eventListTimeFormat: "HH:mm",
  /**
   * A function, or name of a function in the ownership hierarchy which is used to create
   * the time output next to event bars in an agenda cell.
   *
   * The result of this function is used as the content of an element which has
   * the CSS class `'b-cal-eventlist-event-time'`.
   *
   * ```javascript
   * modes :
   *     agenda : {
   *         // Add extra info below the time.
   *         // We set .b-cal-eventlist-event-time to flex-direction : column
   *         eventTimeRenderer({ eventRecord, date }) {
   *             return [
   *                 this.defaultEventTimeRenderer({ eventRecord, date }),
   *                 {
   *                     className : 'event-location',
   *                     text      : eventRecord.location
   *                 }
   *             ];
   *         }
   *     }
   * }
   * ```
   *
   * @prp {Function|String}
   * @param {Scheduler.model.EventModel} eventRecord The event record for which to create a time string.
   * @param {Date} date The date of the cell in which the event is being rendered.
   * @returns {String|DomConfig|DomConfig[]}
   */
  eventTimeRenderer: null
});
/**
 * Returns the resource associated with this event list when used inside a {@link Calendar.widget.ResourceView}
 * @readonly
 * @member {Scheduler.model.ResourceModel} resource
 */
__publicField(EventList, "delayable", {
  populateStoreSoon: {
    type: "raf",
    cancelOutstanding: true
  }
});
EventList.initClass();
EventList._$name = "EventList";

// lib/Calendar/widget/AgendaView.js
var isMouseOverOut = {
  mouseover: 1,
  mouseout: 1
};
var isMouseInteraction = {
  mousedown: 1,
  mouseup: 1,
  click: 1,
  dblclick: 1,
  contextmenu: 1
};
var compareDate = ({ date: lhs }, { date: rhs }) => lhs - rhs;
var AgendaView = class extends EventList.mixin(Responsive_default) {
  /**
   * Returns the resource associated with this agenda view when used inside a {@link Calendar.widget.ResourceView}
   * @readonly
   * @member {Scheduler.model.ResourceModel} resource
   */
  construct(config) {
    const me = this, featuresProp = ObjectHelper.getPropertyDescriptor(this, "features");
    Object.defineProperty(me, "features", {
      get: () => {
        return featuresProp.get.call(this);
      },
      set: (features) => {
        var _a5;
        const f = GridFeatureManager.getInstanceDefaultFeatures(this);
        for (const ft in f) {
          f[ft] = false;
        }
        f.print = (features == null ? void 0 : features.print) || Boolean((_a5 = this.calendar) == null ? void 0 : _a5.features.print);
        featuresProp.set.call(this, f);
      },
      configurable: true
    });
    super.construct(config);
    me.keyMap = CalendarMixin_default().$meta.config.keyMap;
  }
  onCalendarKeyDown(keyEvent) {
    var _a5, _b2;
    const {
      eventContentElement
    } = this, { target } = keyEvent, eventCellSelector = `.${this.dayCellCls}`, eventCell = target.closest(eventCellSelector);
    if (eventCell) {
      switch (keyEvent.key) {
        case "ArrowUp":
          keyEvent.preventDefault();
          (_a5 = ElementVisitor.getVisitor(eventContentElement, eventCellSelector).previous(eventCell)) == null ? void 0 : _a5.focus();
          break;
        case "ArrowDown":
          keyEvent.preventDefault();
          (_b2 = ElementVisitor.getVisitor(eventContentElement, eventCellSelector).next(eventCell)) == null ? void 0 : _b2.focus();
          break;
        default:
          return super.onCalendarKeyDown(keyEvent);
      }
    } else {
      return super.onCalendarKeyDown(keyEvent);
    }
  }
  initScroll() {
    super.initScroll(...arguments);
    this.scrollable.ion({
      scroll: "onAgendaViewScroll",
      scrollEnd: "onAgendaViewScroll",
      thisObj: this
    });
  }
  onResponsiveStateChange({ state, oldState }) {
    var _a5;
    (_a5 = super.onResponsiveStateChange) == null ? void 0 : _a5.call(this, ...arguments);
    if (oldState && (oldState === "small" || state === "small")) {
      this.refresh();
    }
  }
  onAgendaViewScroll() {
    if (!this.scrollingToDate) {
      const { firstVisibleRow } = this;
      firstVisibleRow && (this.topDate = this.getDateFromElement(firstVisibleRow.cells[0]));
    }
  }
  updateRange(range, was) {
    const me = this, { rowManager } = me;
    if (was && rowManager.rowCount) {
      const { dayTime } = me, topRowDate = dayTime.parseKey(rowManager.topRow.id), bottomRowDate = dayTime.parseKey(rowManager.bottomRow.id);
      if (!DateHelper.betweenLesserEqual(me.date, topRowDate, bottomRowDate)) {
        me.scrollToDateOnDateChange = false;
        me._startDate = DateHelper.floor(me._startDate, range, null, me.weekStartDay);
        me.date = me.firstVisibleRow.id;
        me.scrollToDateOnDateChange = true;
        return;
      }
    }
    super.updateRange(...arguments);
  }
  updateTopDate(topDate) {
    this.trigger("topDateChange", { date: topDate });
  }
  changeColumns() {
    var _a5;
    const result = super.changeColumns(...arguments);
    (_a5 = result == null ? void 0 : result.forEach) == null ? void 0 : _a5.call(result, (c) => {
      c.autoHeight = Boolean(!c.isAgendaColumn || (c.renderer !== c.defaultRenderer || this.eventRenderer));
    });
    return result;
  }
  // Override these because CalendarNavigation focuses and navigates *events*, not grid cells.
  onFocusGesture() {
  }
  onGridElementFocus() {
  }
  onGridBodyFocusIn() {
  }
  focusCell() {
  }
  setHoveredRow() {
  }
  onElementKeyDown() {
  }
  onElementMouseDown() {
  }
  editAutoCreatedEvent(event, eventRecord) {
    this.populateStoreSoon.now();
    super.editAutoCreatedEvent(event, eventRecord);
  }
  handleEvent(event) {
    const { type } = event;
    super.handleEvent(event);
    if (isMouseOverOut[event.type]) {
      this.onEventMouseOverOut(event);
    } else if (this.consumeGridPointerEvents !== false && isMouseInteraction[type]) {
      this.onCalendarPointerInteraction(event);
    }
  }
  getCellDataFromEvent(domEvent) {
    if (domEvent.target.closest(".b-grid-cell")) {
      const result = super.getCellDataFromEvent(domEvent);
      result && (result.record = this.getEventRecord(domEvent.target));
      return result;
    }
  }
  getDateFromPosition(x, y) {
    const row = this.rowManager.getRowAt(y), cell = row == null ? void 0 : row._cells.normal[0];
    return cell && this.getDateFromElement(cell);
  }
  updateOffsetStartsBeforeEvents(offsetStartsBeforeEvents) {
    this.element.classList.toggle("b-offset-continues-past", Boolean(offsetStartsBeforeEvents));
  }
  updateEventRowSpacing(eventRowSpacing) {
    this.contentElement.style.setProperty("--event-row-spacing", DomHelper.setLength(eventRowSpacing));
  }
  updateSuppressLongEvents() {
    this.fillFromMaster();
  }
  onCalendarStoreChange({ action, oldCount, records, removed, added }) {
    const me = this;
    if (me.isPainted) {
      if (action === "filter" && !(removed == null ? void 0 : removed.length) && !(added == null ? void 0 : added.length)) {
        return;
      }
      if (action === "dataset") {
        return;
      }
      if (action === "remove" && records.length === 1 && records[0].isEventModel) {
        const { store } = me, eventRecord = records[0], date2 = DateHelper.clearTime(eventRecord.startDate), endDate = DateHelper.clearTime(eventRecord.endDate);
        do {
          const key = DateHelper.makeKey(date2), cellData = me.cellMap.get(key);
          if (cellData) {
            ArrayHelper.remove(cellData.events, eventRecord);
            if (cellData.events.length) {
              me.onStoreUpdateRecord({
                source: store,
                record: me.store.getById(key),
                changes: {}
              });
            } else {
              me.cellMap.delete(key);
              delete me.dateIndex[key];
              store.remove(key, true);
            }
          }
          date2.setDate(date2.getDate() + 1);
        } while (date2 < endDate);
        return;
      }
    }
    me.populateStoreSoon();
  }
  get cellMap() {
    var _a5;
    const me = this;
    return ((_a5 = me._cellMap) == null ? void 0 : _a5.populated) ? me._cellMap : me.createCellMap({
      // If we are being set a startDate and endDate, as opposed to using a fixed "range"
      // around a date, then to provide a more intuitive interface, we *include* the endDate
      // for EventLists
      endDate: DateHelper.add(me.endDate, me.range ? 0 : 1, "d"),
      rawEvents: true,
      skipPropagate: true
    });
  }
  onMonthChange() {
    var _a5;
    super.onMonthChange(...arguments);
    if (!((_a5 = this._cellMap) == null ? void 0 : _a5.size) && this.alwaysShowCurrentDate) {
      this.populateStoreSoon();
    }
  }
  populateStore() {
    var _a5, _b2;
    (_a5 = this._cellMap) == null ? void 0 : _a5.clear();
    const me = this, {
      store,
      rowManager,
      dateFilter,
      hiddenNonWorkingDays,
      dayTime,
      scrollable,
      initialScrollDone
    } = me, eventHeight = isNaN(me.eventHeight) ? 25 : me.eventHeight, { firstVisibleRow } = rowManager, firstVisibleId = firstVisibleRow == null ? void 0 : firstVisibleRow.id, firstVisibleEdgeOffset = firstVisibleRow ? firstVisibleRow.top - scrollable.y : 0;
    me._eventCount = 0;
    me.populateStoreSoon.cancel();
    me.element.dataset.duration = me.duration;
    if (!me.date) {
      me.isConfiguring = true;
      me.date = me.eventStore.map((r) => r.startDate).sort((lhs, rhs) => lhs.valueOf() - rhs.valueOf())[0];
      me.isConfiguring = false;
    }
    const {
      cellMap,
      firstVisibleDate,
      eventRowSpacing
    } = me, endDate = DateHelper.add(me.endDate, me.range ? 0 : 1, "d"), cellMapEntries = me.showEmptyDates ? me.createRangeCellMapEntries() : [...cellMap.values()], sortedEntries = [], dateIndex = me.dateIndex = {};
    for (let i = 0, { length } = cellMapEntries; i < length; i++) {
      const cellData = cellMapEntries[i], { date: date2 } = cellData;
      if (date2 >= firstVisibleDate && date2 < endDate && (!dateFilter || me.callback(dateFilter, me, [cellData])) && !hiddenNonWorkingDays[date2.getDay()]) {
        const cellInfo = store.createRecord(cellData), idx = ArrayHelper.findInsertionIndex(cellInfo, sortedEntries, compareDate);
        sortedEntries.splice(idx, 0, cellInfo);
        dateIndex[cellData.id] = cellInfo;
      }
    }
    if (me.alwaysShowCurrentDate && !(store.isFiltered ? sortedEntries.filter(store.filtersFunction) : sortedEntries).length) {
      const currentDateRec = store.createRecord({
        ...me.createCellData(me.date),
        cls: "b-current-date-placeholder"
      });
      sortedEntries.push(currentDateRec);
      dateIndex[currentDateRec.id] = currentDateRec;
    }
    store.suspendEvents();
    store.loadData(sortedEntries);
    store.resumeEvents();
    me.onAgendaStoreFilter();
    const { count } = store, avgEventsPerCell = count ? store.records.map((e) => e.events.length).reduce((a, b) => a + b, 0) / count : 0, newDate = me.findClosestOccupiedDate(me.date), targetRec = store.getById(initialScrollDone ? firstVisibleId : dayTime.dateKey(newDate));
    rowManager._rowHeight = rowManager.minRowHeight = Math.max(avgEventsPerCell * (eventHeight + eventRowSpacing) - eventRowSpacing, 70);
    rowManager.calculateRowCount(false, true, true);
    rowManager.estimateTotalHeight(true);
    if (count) {
      const { topRow } = rowManager, targetIndex = Math.max(store.indexOf(targetRec) - Math.round(scrollable.viewport.height / rowManager.rowHeight) - rowManager.prependRowBuffer, 0);
      rowManager.topIndex = topRow.dataIndex = targetIndex;
      topRow.setTop(rowManager.rowHeight * targetIndex);
    }
    rowManager.refresh();
    if (targetRec) {
      const recordCoords = rowManager.getRecordCoords(targetRec, true), scrollOptions = initialScrollDone ? {
        animate: false,
        block: "start",
        edgeOffset: firstVisibleEdgeOffset
      } : {
        animate: false,
        block: "end"
      };
      scrollable.y += scrollable.getDeltaTo(recordCoords, scrollOptions).yDelta;
      rowManager.lastScrollTop = scrollable.y;
      me.scrollTo(targetRec, scrollOptions);
      me.initialScrollDone = true;
    }
    me.refreshCount = (me.refreshCount || 0) + 1;
    me.trigger("refresh");
    (_b2 = me.calendar) == null ? void 0 : _b2.syncUIWithActiveView(me);
    me.columns.forEach((c) => {
      var _a6, _b3;
      return (_b3 = (_a6 = c.constructor).exposeProperties) == null ? void 0 : _b3.call(_a6);
    });
    me.getConfig("settingsButton");
  }
  createRangeCellMapEntries() {
    const me = this, {
      cellMap,
      startDate,
      endDate
    } = me, entries = [];
    for (const date2 = new Date(startDate); date2 < endDate; date2.setDate(date2.getDate() + 1)) {
      entries.push(cellMap.get(DateHelper.makeKey(date2)) || me.createCellData(date2));
    }
    return entries;
  }
  // Override.
  // We are empty if the sole record has no events.
  // Because ths will be the injected "alwaysShowCurrentDate" record which has no events.
  // No other record can be empty.
  toggleEmptyText() {
    var _a5;
    const { store } = this, count = store.count === 1 && !((_a5 = store.first) == null ? void 0 : _a5.events.length) ? 0 : store.count, isEmpty = !(count > 0 || store.isLoading || store.isCommitting);
    this.element.classList.toggle("b-grid-empty", isEmpty);
  }
  scrollRowIntoView(recordOrId) {
    const { scrollable, rowManager } = this, record = this.store.getById(recordOrId);
    if (!rowManager.getRowFor(record)) {
      const agendacolumn = this.columns.find((c) => c.type === "agendacolumn");
      if (agendacolumn.renderer === agendacolumn.defaultRenderer) {
        const targetRowTop = this.calculateRowTop(record);
        scrollable.y = targetRowTop < rowManager.firstVisibleRow.top ? targetRowTop + rowManager.averageRowHeight : targetRowTop - scrollable.clientHeight;
        rowManager.jumpToPosition(scrollable.y);
      }
    }
    super.scrollRowIntoView(...arguments);
  }
  /**
   * Calculate the Y position of a row in the view accurately using the known event height
   * and event row spacing and cell padding.
   * @param {Grid.data.GridRowModel} record The record to calculate the top position of.
   * @returns {Number} The Y position of the row.
   * @private
   */
  calculateRowTop(record) {
    var _a5;
    let recTop = 0;
    if (this.rowManager.rowCount) {
      const me = this, eventHeight = isNaN(me.eventHeight) ? 25 : me.eventHeight, { eventRowSpacing } = me, cell = me.rowManager.topRow.cells[0], cellPadding = DomHelper.getEdgeSize(cell, "padding", "tb").height, minRowHeight = (((_a5 = cell.querySelector(".b-cal-agenda-date")) == null ? void 0 : _a5.offsetHeight) || 0) + cellPadding;
      for (let i = 0, { count } = me.store; i < count; i++) {
        const rec = me.store.getAt(i), recHeight = Math.max(rec.events.length * (eventHeight + eventRowSpacing) - eventRowSpacing + cellPadding, minRowHeight);
        if (rec === record) {
          break;
        }
        recTop += recHeight;
      }
    }
    return recTop;
  }
  // Overridden from AgendaView which uses its own count value
  get eventCount() {
    return this._eventCount;
  }
  collectEvents(options) {
    options.getDateIndex = (date2) => Number(date2) === Number(this.startDate) || !this.hideEventOverflow ? "date" : "startDate";
    return super.collectEvents(options);
  }
  changeStore(store) {
    store = super.changeStore(store);
    if (store) {
      this.nonWorkingDaysFilter = store.addFilter({
        id: `${this.id}-nonworkingday-filter`,
        filterBy: (rec) => !rec.isNonWorking,
        disabled: !this.hideNonWorkingDays
      }, true);
      this.detachListeners("agendaStoreFilter");
      store.ion({
        name: "agendaStoreFilter",
        filter: "onAgendaStoreFilter",
        thisObj: this
      });
      store.reapplyFilterOnAdd = true;
    }
    return store;
  }
  onAgendaStoreFilter() {
    const me = this;
    me._eventCount = me.store.reduce((result, rec, i) => {
      const { events, date: date2 } = rec;
      for (let j = 0, { length } = events; j < length; j++) {
        const event = events[j];
        if (!me.isAllDayEvent(event) || !i || DateHelper.clearTime(event.startDate).valueOf() === date2.valueOf()) {
          result++;
        }
      }
      return result;
    }, 0);
  }
  updateHideEventOverflow() {
    this.refresh();
  }
  updateHideNonWorkingDays(hideNonWorkingDays) {
    if (!this.isConfiguring) {
      this.nonWorkingDaysFilter.disabled = !hideNonWorkingDays;
    }
    this.refresh();
  }
  // We must implement the CalendarMixin interface.
  // All views must expose a doRefresh method.
  // Override from EventList. We need to repopulate our store to create day cells.
  doRefresh() {
    this.populateStore();
  }
  createCellData(date2) {
    return Object.assign(this.cellMonth.getCellData(date2, this.month), {
      view: this,
      id: DateHelper.makeKey(date2),
      events: []
    });
  }
  set cellRenderer(cellRenderer) {
    this._cellRenderer = cellRenderer;
  }
  changeSettingsButton(settings) {
    const { listRangeMenu: menu } = this;
    menu && (menu.appendTo = this.element);
    settings = settings && menu && super.changeSettingsButton(this.constructor.mergeConfigs({ menu }, settings));
    if (settings) {
      this.addKeyBinding(settings.actionKey, () => {
        settings.focus;
        settings.element.click();
      });
    }
    return settings;
  }
};
__publicField(AgendaView, "$name", "AgendaView");
__publicField(AgendaView, "type", "agendaview");
__publicField(AgendaView, "configurable", {
  eventSorter: (event1, event2) => {
    const {
      startDate: start1,
      isInterDay: event1InterDay
    } = event1, {
      startDate: start2,
      isInterDay: event2InterDay
    } = event2;
    if (event1InterDay !== event2InterDay) {
      return Number(event2InterDay) - Number(event1InterDay);
    }
    if (!event1InterDay && !event2InterDay) {
      return start1 - start2;
    }
    return start1 - start2 || event2.durationMS - event1.durationMS || event2.endingDate - event1.endingDate;
  },
  /**
   * The spacing between event bars in the default rendering of a day cell.
   * @config {Number|String}
   * @default
   */
  eventRowSpacing: 8,
  title: "L{Agenda}",
  range: "year",
  /**
   * By default, long running events are repeated in all rows that the event covers.
   *
   * Configure this as `true` to only see the start of a long running event in its
   * start day.
   * @config {Boolean}
   */
  hideEventOverflow: null,
  /**
   * This view lines up the textual content of event bars by shifting event bars of
   * events which start before the bar's cell leftwards by the arrow width.
   *
   * Set this config to `false` to prevent this.
   * @prp {Boolean}
   * @default
   */
  offsetStartsBeforeEvents: true,
  // We handle this internally using the notifications from the GridElementEvents mixin
  handlePointerInteraction: false,
  /**
   * Specify `false` to display column headers
   * @config {Boolean}
   * @default
   * @category Misc
   */
  hideHeaders: true,
  /**
   * Column definitions.
   *
   * By default, a single {@link Calendar.column.AgendaColumn} is configured which creates
   * the default cell content for one day's events.
   *
   * You may configure the default agenda column away, and provide a custom column type
   * to produce the day's content where the `record` passed is a
   * {@link Calendar.widget.mixin.DayCellCollecter#typedef-DayCell}.
   *
   * Because cells may contain varying numbers of events, all columns in an AgendaView are
   * set to {@link Grid.column.Column#config-autoHeight}
   *
   * ```javascript
   * class MyAgendaColumn extends Column {
   *     // So we automatically get b-myagenda-cell class on the cells
   *     static get type() {
   *         return 'myagenda';
   *     }
   *
   *     renderer({ cellElement, record : cellData }) {
   *         // Create a DomHelper element configuration object here using cellData
   *         // cellData contains date contextual info and an events array.
   *     }
   * }
   *
   * ...
   *
   * {
   *     columns : {
   *         agenda : null,
   *         {
   *             type : 'mycolumntype'
   *         }
   *     }
   * }
   *
   * // Register this Column type so that in the app we can use type : 'myagendacolumn'
   * ColumnStore.registerColumnType(MyAgendaColumn);
   * ```
   *
   * @config {Object|Object[]}
   * @default { agenda : { type : 'agendacolumn' } }
   */
  columns: {
    // We knock out the columns we inherit from EventList.
    name: null,
    startDate: null,
    endDate: null,
    resources: null,
    agenda: {
      type: "agendacolumn"
    }
  },
  enableSticky: true,
  preserveScrollOnDatasetChange: true,
  positionMode: "position",
  /**
   * The "Settings" button which is a floating button which offers the Agenda {@link #property-listRangeMenu range selection menu}
   * @prp {Core.widget.Button}
   * @property {String} actionKey The composed key name to press to open the settings menu. Default is 'CTRL+R'
   * @accepts {ButtonConfig}
   */
  settingsButton: {
    actionKey: "Ctrl+R",
    ariaLabel: "L{settings}",
    keyMap: {
      Escape: "revertFocus"
    }
  },
  cellTabIndex: null,
  rowCls: {
    "b-cal-agenda-grid-row": 1
  },
  cellCls: {
    "b-calendar-cell": 1
  },
  eventBarContainerCls: "b-cal-event-bar-container",
  // No GridNavigation key events in an AgendaView. It is natural, event-to-event navigation.
  keyMap: null,
  // Our rows encapsulate dates and *contain* events, so they are raw GridRowModels
  store: {
    modelClass: GridRowModel
  },
  listRangeMenu: {
    align: {
      align: "t100-b100"
    }
  },
  /**
   * A callback function, or the name of a function in the ownership hierarchy which an
   * application may provide to filter out agenda rows for individual dates.
   * @prp {Function|String} dateFilter
   * @param {DayCell} context Information about the day rows to be created.
   * @returns {Boolean} Return `false` to not display a rows for the passed date.
   */
  dateFilter: null,
  /**
   * If no dates in the view's configured date range contain events, then by default, no date cells are rendered.
   *
   * Set this to `true` to always show a cell for the calendar's {@link Calendar.view.Calendar#property-date} in
   * order to allow users to interact with the view, for example by using the context menu, or the
   * {@link #config-autoCreate} gesture.
   *
   * @prp {Boolean}
   * @default false
   */
  alwaysShowCurrentDate: null,
  /**
   * By default, only cells for days which contain events are shown.
   *
   * If set to `true` the view will include cells for days which contain no events.
   * @prp {Boolean}
   * @default false
   */
  showEmptyDates: null,
  emptyText: "L{YearView.noEvents}",
  topDate: {
    $config: {
      equal: "date"
    },
    value: null
  }
});
AgendaView.initClass();
AgendaView._$name = "AgendaView";

// lib/Calendar/layout/LayoutDim.js
var noCalcRe = /^[+-]?\d+(?:\.\d+)?(?:%|px)?$/;
var LayoutDim = class _LayoutDim {
  static from(value2) {
    if (value2 == null) {
      return null;
    }
    if (value2 instanceof _LayoutDim) {
      return value2;
    }
    if (!(Array.isArray(value2) || typeof value2 === "string" && isNaN(value2))) {
      value2 = Number(value2);
      value2 = value2 === 0 ? [0, new Number(0)] : value2 < 1 ? [value2, 0] : [0, value2];
    }
    return new _LayoutDim(...value2);
  }
  static get(value2) {
    return _LayoutDim.from(value2) || _LayoutDim.ZERO;
  }
  constructor(ratio, delta) {
    this.r = ratio;
    this.d = delta;
  }
  add(v) {
    v = _LayoutDim.from(v);
    return v ? new _LayoutDim(this.r + v.r, this.d + v.d) : this;
  }
  adjust(r, d) {
    this.r += r || 0;
    this.d += d || 0;
    return this;
  }
  freeze() {
    return Object.freeze(this);
  }
  px(range) {
    return this.r * (range || 100) + this.d;
  }
  sub(v) {
    v = _LayoutDim.from(v);
    return v ? new _LayoutDim(this.r - v.r, this.d - v.d) : this;
  }
  *[Symbol.iterator]() {
    yield "r";
    yield "d";
  }
  stringify() {
    const str = this.toString();
    return noCalcRe.test(str) ? str : `calc(${str})`;
  }
  toString() {
    const { r, d } = this;
    let str;
    if (r) {
      str = DomHelper.percentify(r * 100);
      if (d) {
        str = `${str} + ${d}px`.replace("+ -", "- ");
      }
    } else {
      str = d ? `${d}px` : "0";
    }
    return str;
  }
};
LayoutDim.ZERO = new LayoutDim(0, 0).freeze();
LayoutDim._$name = "LayoutDim";

// lib/Calendar/layout/day/DayLayout.js
var getStyles = function(rtl) {
  var _a5, _b2;
  const me = this;
  return {
    [rtl ? "right" : "left"]: (_a5 = me.left) == null ? void 0 : _a5.stringify(),
    top: me.top.stringify(),
    width: (_b2 = me.width) == null ? void 0 : _b2.stringify(),
    height: me.height.stringify(),
    minWidth: me.minWidth
  };
};
var DayLayout = class extends Base.mixin(Factoryable_default) {
  /**
   * This method is called prior to performing the layout in {@link #function-layoutEvents}.
   * @param {DayLayoutContext} context The layout context
   * @returns {DayLayoutContext}
   * @internal
   */
  beforeLayoutEvents(context) {
    this.owner.trigger("beforeLayoutEvents", {
      context
    });
    return context;
  }
  /**
   * This method packages items into clusters of overlapping items.
   * @param {DayLayoutCluster[]} clusters The cluster array being produced. This method adds to this array.
   * @param {DayLayoutItem[]} items The items array being produced. This method adds `item` to this array.
   * @param {DayLayoutItem} item The item to add and pack into a cluster.
   * @param {DayLayoutContext} context The layout context
   * @internal
   */
  clusterize(clusters, items2, item, context) {
    const cluster = clusters[clusters.length - 1] || null;
    items2.push(item);
    if (cluster && this.overlaps(cluster, item)) {
      item.cluster = cluster;
      cluster.end = Math.max(cluster.end, item.end);
      cluster.items.push(item);
    } else {
      clusters.push(item.cluster = {
        context,
        start: item.start,
        end: item.end,
        items: [item]
      });
    }
  }
  /**
   * Populates and returns a layout context used for the layout process.
   * @param {DayCell} cellData An object containing information about the day cell being created.
   * @param {DomConfig} [dayDomConfig] The DomConfig element definition for the day cell into which events are being
   * rendered.
   * @returns {DayLayoutContext}
   * @internal
   */
  createLayoutContext(cellData, dayDomConfig) {
    const events = cellData == null ? void 0 : cellData.events;
    return {
      cellData,
      dayDomConfig,
      // We need the events for the day to be sorted by startDate but limited to the start of the day. In
      // other words, all events that start before "midnight" are equally considered as starting at midnight.
      // This order of events is an assumption that runs deep in FluidDayLayout but also in clusterization.
      events: (events == null ? void 0 : events.length) > 1 ? cellData.dayTime.sortEvents(cellData.date, events.slice()) : events || [],
      clusters: [],
      items: [],
      layout: this
    };
  }
  /**
   * Calculate the placements for the given events within the specified time range.
   * @param {DayCell} cellData An object containing information about the day cell being created.
   * @param {DomConfig} [dayDomConfig] The DomConfig element definition for the day cell into which events are being
   * rendered.
   * @returns {DayLayoutContext}
   * @internal
   */
  layoutEvents(cellData, dayDomConfig) {
    const me = this, context = me.beforeLayoutEvents(me.createLayoutContext(cellData, dayDomConfig)), { clusters, events, items: items2 } = context;
    let event, i, item, n;
    for (event of events) {
      item = me.createLayoutItem(event, context);
      me.clusterize(clusters, items2, item, context);
    }
    for (i = 0, n = clusters.length; i < n; ++i) {
      me.layoutCluster(clusters[i]);
    }
    me.owner.trigger("layoutEvents", {
      context
    });
    return context;
  }
  /**
   * Returns the base of a {@link Calendar.layout.day.DayLayout#typedef-DayLayoutItem} for the passed
   * event record.
   * @param {Scheduler.model.EventModel} eventRecord The event record for which to create a layout item.
   * @param {DayLayoutContext} context The layout context
   * @returns {DayLayoutItem}
   * @internal
   */
  createLayoutItem(eventRecord, context) {
    const { date: date2, tomorrow, dayEnd, dayTime } = context.cellData, { allDay, startDate, endingDate } = eventRecord, startSec = allDay || startDate < date2 ? 0 : dayTime.delta(startDate, "s"), preamble = Duration.from(eventRecord.preamble), postamble = Duration.from(eventRecord.postamble);
    let endSec = allDay || endingDate >= tomorrow ? dayEnd : dayTime.delta(endingDate, "s");
    if (eventRecord.isMilestone && !eventRecord.isTimeRangeModel && !eventRecord.allDay) {
      endSec = startSec + this.owner.milestoneDuration / 1e3;
    }
    const start = Math.max(startSec, 0), end = Math.min(endSec, dayEnd), top = LayoutDim.from(start / dayEnd);
    return {
      eventRecord,
      getStyles,
      start,
      end,
      top,
      id: eventRecord.id,
      height: new LayoutDim(end / dayEnd, 0).sub(top),
      startsBefore: startDate < date2 || startSec < 0,
      endsAfter: tomorrow < endingDate || endSec > dayEnd,
      preambleSec: preamble ? DateHelper.as("s", preamble) : 0,
      postambleSec: postamble ? DateHelper.as("s", postamble) : 0
    };
  }
  overlaps(a, b, tolerance, end) {
    if (typeof tolerance === "string") {
      end = tolerance;
      tolerance = null;
    }
    end = end || "end";
    tolerance = tolerance != null ? tolerance : this.overlapTolerance * 60 || 0;
    return a.start < b[end] - tolerance && b.start < a[end] - tolerance;
  }
  syncGutter() {
    this.owner.setStyle("--dayview-cell-gutter", String(LayoutDim.get(this.gutter && this.gutterWidth)));
  }
  updateGutter() {
    this.syncGutter();
  }
  updateGutterWidth() {
    this.syncGutter();
  }
  updateInset(inset) {
    this.owner.setStyle("--dayview-cell-inset-size", String(LayoutDim.get(inset)));
  }
};
__publicField(DayLayout, "factoryable", {
  // establish this class as the Factoryable base
});
__publicField(DayLayout, "configurable", {
  /**
   * Set to `false` to disable the gap on the right-most edge of events. See {@link #config-gutterWidth} to
   * control the size of the gutter.
   *
   * The gutter allows the user to select the times overlapped by events, for example, to create new events.
   * @config {Boolean}
   * @default
   */
  gutter: true,
  /**
   * The number of pixels or proportion of the overall width to allocate to the {@link #config-gutter}.
   *
   * Values less than 1 are the fractional proportion of the width (for example, 0.04 is 4% of the width),
   * while values greater than or equal to 1 are a number of pixels.
   * @config {Number}
   * @default
   */
  gutterWidth: 5,
  /**
   * The number of pixels or proportion of the overall width to allocate for rendering on outside the area where
   * events are displayed. This space is used by {@link Calendar.feature.TimeRanges} to render arbitrary spans of
   * time on either side of the day's events.
   *
   * Values less than 1 are the fractional proportion of the width (for example, 0.04 is 4% of the width),
   * while values greater than or equal to 1 are a number of pixels.
   * @config {Number}
   * @default
   */
  inset: 40,
  /**
   * The minimum number of minutes an event must overlap another event before it is considered an overlap
   * for layout purposes.
   *
   * The default value of 0 treats any overlap in time as an overlap in the layout.
   * @config {Number}
   * @default 0
   * @internal
   */
  overlapTolerance: null,
  owner: null
});
DayLayout._$name = "DayLayout";

// lib/Calendar/layout/day/FluidDayLayout.js
var Packer = class extends PackMixin_default() {
  static get defaultConfig() {
    return {
      coordProp: "left",
      sizeProp: "width"
    };
  }
  pack(items2) {
    const wrappedItems = items2.map((item) => {
      const { startDate } = item.eventRecord, endDate = new Date(startDate);
      endDate.setSeconds(endDate.getSeconds() + item.maxEnd - item.start);
      return {
        item,
        start: startDate,
        end: endDate,
        x: 0
      };
    });
    this.packEventsInBands(wrappedItems, (itemData, clusterIndex, slot, slotSize) => {
      itemData.width = slotSize;
      itemData.w = slotSize * COLUMN_WIDTH;
      itemData.x += (itemData.left = slot.start + clusterIndex * slotSize) * COLUMN_WIDTH;
      itemData.item.x = itemData.x;
      itemData.item.w = itemData.w;
    });
  }
};
var COLUMN_WIDTH = 1e4;
var EMPTY3 = Object.freeze([]);
var packer = new Packer();
var FluidDayLayout = class extends DayLayout {
  createLayoutContext(cellData, dayDomConfig) {
    const me = this, context = super.createLayoutContext(cellData, dayDomConfig), { staggerWidth } = me;
    return Object.assign(context, {
      indentWidth: LayoutDim.get(me.indentWidth),
      stagger: new LayoutDim(),
      // just a reused instance
      staggerMaximum: LayoutDim.from(me.staggerMaximum),
      // can be null
      staggerMinimum: me.staggerMinimum,
      staggerWidth: LayoutDim.from(staggerWidth === true ? me.gutterWidth : staggerWidth),
      stretch: me.stretch
    });
  }
  layoutCluster(cluster) {
    const me = this, { context, items: items2 } = cluster, {
      indentWidth,
      stagger,
      staggerMaximum,
      staggerMinimum,
      staggerWidth,
      stretch
    } = context, treeLevels = me.treeify(items2);
    me._assignSizes(items2, treeLevels);
    for (const item of items2) {
      const depth = item.depth, left = new LayoutDim(), width = new LayoutDim(), root = item.majorParent || item;
      for (const k of stagger) {
        stagger[k] = staggerWidth[k] * (depth.height + depth.overlap);
        stagger[k] = (staggerMaximum == null ? void 0 : staggerMaximum[k]) ? Math.min(staggerMaximum[k], stagger[k]) : stagger[k];
      }
      left.r = root.x + depth.minor * indentWidth.r * root.w;
      left.d = depth.minor * indentWidth.d;
      width.r = stretch ? 1 - left.r : root.w - left.r + root.x;
      width.d = -left.d;
      if (staggerMinimum) {
        item.minWidth = `calc(${staggerMinimum} * (${width}))`;
      }
      width.r -= stagger.r * root.w;
      width.d -= stagger.d;
      item.left = left;
      item.width = width;
    }
  }
  treeify(items2) {
    const treeLevels = [];
    for (let i = 0; i < items2.length; i++) {
      this._addChild(items2[i], i, treeLevels);
    }
    return treeLevels;
  }
  _addChild(item, order, treeLevels) {
    const me = this, { clearanceMinutes, escapeMinutes, stretch } = me, clearanceSeconds = clearanceMinutes * 60, escapeSeconds = clearanceSeconds + escapeMinutes * 60, overlapTolerance = (me.overlapTolerance || 0) * 60, isMinor = (a, b) => Math.abs(b.start - a.start) >= clearanceSeconds, scoreFn = (item2) => item2.depth.minor * 10 + item2.depth.major * (stretch ? 1 : 0), depth = {
      // The true depth from the root of the tree, regardless of major/minor distinctions.
      depth: 0,
      // The number of steps up from the deepest minor child to this node.
      height: 0,
      // The number of steps up from the deepest major child to this node.
      heightMajor: 0,
      // The number of items this item overlaps. This happens when a node clears the end time of a higher
      // level node and is elevated. This ensures that the bottom of these overlapped events are exposed
      // by the staggered width of this item. See item 2 in example C below.
      overlap: 0,
      // The number of major overlaps for this item and its ancestry. More space is allocated in this case
      // then for minor overlaps. The number of minor overlaps per major overlap is held in "clearanceScale".
      major: 0,
      // The number of minor overlaps for this item and its ancestry. When there is sufficient content shown
      // for a parent, a child can be indented by much less without hiding those details. This minimal amount
      // of overlap is specified in seconds via "clearanceSeconds".
      minor: 0
    };
    item.parent = null;
    item.maxEnd = item.maxEndMajor = item.end;
    item.depth = depth;
    item.order = order;
    if (!treeLevels.length) {
      treeLevels[0] = item;
      item.barriers = EMPTY3;
      return;
    }
    let best = 0, child, major, minor, p, parent, score;
    for (p of treeLevels) {
      child = p.lastItem || p;
      if (!me.overlaps(item, p, overlapTolerance, "maxEnd")) {
        parent = p.parent;
        major = true;
        break;
      }
      if (isMinor(item, child)) {
        minor = true;
        if (major) {
          major = false;
          parent = null;
        }
        for (; child !== p; child = child.parent) {
          if (me.overlaps(child, item, overlapTolerance)) {
            break;
          }
        }
        score = scoreFn(child);
        if (!parent || score < best) {
          parent = child;
          best = score;
        }
      } else if (!minor) {
        major = true;
        parent = p;
      }
    }
    if (major) {
      minor = 0;
      major = parent ? parent.depth.major + 1 : 0;
      p = parent = treeLevels[major - 1] || null;
    } else {
      p = parent.majorParent || parent;
      for (; escapeSeconds && parent !== p; parent = parent.parent) {
        if (me.overlaps(parent, item, overlapTolerance) && item.start - parent.start < escapeSeconds) {
          break;
        }
      }
      minor = parent.depth.minor + 1;
      major = parent.depth.major;
    }
    item.parent = parent;
    depth.major = major;
    depth.minor = minor;
    depth.depth = parent ? parent.depth.depth + 1 : 0;
    if (minor) {
      item.majorParent = p;
      item.previousItem = p.lastItem;
      p.lastItem = item;
    } else {
      me._addMajor(item, treeLevels);
    }
    me._adjustOverlap(item);
    me._adjustParents(item);
  }
  _addMajor(item, treeLevels) {
    const me = this, { stretch } = me, level = item.depth.major;
    treeLevels[level] = item;
    item.barriers = me._getOverlaps(treeLevels, item, 1);
    if (stretch || item.barriers.length) {
      for (const prior of me._getOverlaps(treeLevels, item, -1)) {
        const barriers = prior.barriers.filter((b) => !item.barriers.includes(b));
        if (stretch || barriers.length < prior.barriers.length) {
          prior.barriers = barriers;
          barriers.push(item);
        }
      }
    }
  }
  _adjustOverlap(item) {
    if (item.depth.minor) {
      let overlap, p;
      for (p = item; p.depth.minor > 1; p = p.parent) {
      }
      for (overlap = 0; p = p.previousItem; ) {
        if (this.overlaps(p, item)) {
          overlap = Math.max(overlap, p.depth.height + p.depth.overlap + 1);
        }
      }
      item.depth.overlap = overlap;
    }
  }
  _adjustParents(item) {
    const { depth, end } = item, level = depth.minor + depth.overlap;
    for (let parentDepth, p = item; p = p.parent; ) {
      parentDepth = p.depth;
      if (parentDepth.major !== depth.major) {
        parentDepth.heightMajor = Math.max(parentDepth.heightMajor, depth.major - parentDepth.major);
        continue;
      }
      p.maxEnd = Math.max(p.maxEnd, end);
      while (parentDepth.height < level - parentDepth.minor) {
        ++parentDepth.height;
        if (parentDepth.overlap) {
          --parentDepth.overlap;
        }
      }
    }
  }
  _assignSizes(items2, treeLevels) {
    const { complexityLimit, stretch } = this, topLevelItems = items2.filter((it) => !it.depth.minor);
    if (!complexityLimit) {
      packer.pack(topLevelItems);
      for (const item of topLevelItems) {
        item.x /= COLUMN_WIDTH;
        item.w /= COLUMN_WIDTH;
      }
    } else {
      for (const item of topLevelItems) {
        const paths = item.barriers.map((b) => [b]), { parent } = item, height = item.depth.heightMajor + 1;
        item.x = parent ? parent.x + parent.w : 0;
        let w = (1 - item.x) / height, i, include, longest, path, tail, v, x;
        if (paths.length) {
          for (i = 0; i < paths.length; ) {
            path = paths[i];
            longest = longest || path;
            if (i > complexityLimit) {
              w = 1 / treeLevels.length;
              break;
            }
            tail = path[path.length - 1];
            if (tail.order < item.order) {
              ++i;
              x = tail.x;
              include = 0;
            } else if (!tail.barriers.length) {
              ++i;
              x = 1;
              include = 1;
            } else {
              paths.splice(i, 1, ...tail.barriers.map((b) => path.concat(b)));
              if (longest === path) {
                longest = null;
              }
              continue;
            }
            v = (x - item.x) / (include + path.length);
            if (v < w) {
              w = v;
              longest = path;
            }
          }
          if (stretch) {
            v = longest.reduce((s, it) => s + it.depth.height + 1, 0);
            for (i = item.lastItem; i; i = i.previousItem) {
              i.depth.overlap += v;
            }
            item.depth.overlap += v;
          }
        }
        item.w = w;
      }
    }
  }
  _getOverlaps(treeLevels, item, step) {
    const { end } = item, stop = step < 0 ? -1 : treeLevels.length;
    let { start } = item, i, other, ret;
    for (i = item.depth.major + step; i !== stop && start < end; i += step) {
      other = treeLevels[i];
      if (start < other.maxEnd && other.start < end) {
        (ret || (ret = [])).push(other);
        start = other.maxEnd;
      }
    }
    return ret || EMPTY3;
  }
};
__publicField(FluidDayLayout, "type", "fluid");
__publicField(FluidDayLayout, "configurable", {
  /**
   * The number of minutes of an event that must be (vertically) cleared by another event before it is
   * allowed to be significantly overlapped by it (horizontally).
   *
   * For example:
   *
   * ```text
   *       >= clearanceMinutes          < clearanceMinutes
   *      +--------------+            +----------------+
   *      | Event 1      |            | Event 3        |
   *      | 9am-11am     |            | 9am-11am +-------------+
   *      |  +----------------+       |          | Event 4     |
   *      |  | Event 2        |       |          | 9:30am-12pm |
   *      |  | 10am-12pm      |       |          |             |
   *      |  |                |       |          |             |
   *      +--|                |       +----------|             |
   *         |                |                  |             |
   *         +----------------+                  +-------------+
   * ```
   *
   * In the example above, the start time of Event 2 is more than `clearanceMinutes` after the start time of
   * Event 1, therefore it is displayed with a minimal indent from the left of Event 1. The start time of
   * Event 4, however, is less than `clearanceMinutes` after Event 3 and so they both share the space evenly.
   * @config {Number}
   * @default
   */
  clearanceMinutes: 45,
  /**
   * The maximum number of possible solutions to evaluate when calculating an event's width.
   * @config {Number}
   * @internal
   * @default
   */
  complexityLimit: 2e3,
  /**
   * The number of minutes of an overlapping event that must be (vertically) cleared by another event before
   * it is allowed to be fully overlapped by it (horizontally). THis value is a number of minutes _beyond_
   * {@link #config-clearanceMinutes}. In other words, if `clearanceMinutes` is 45 and this config is set to
   * 75, the start time of an event must be two hours (45+75 = 120 minutes) after the prior event to fully
   * overlap it.
   *
   * For example:
   *
   * ```text
   *       >= escapeMinutes                    < escapeMinutes
   *      +----------------+                 +----------------+
   *      | Event 1        |                 | Event 5        |
   *      | 9am-11am +-------------+         | 9am-11am +-------------+
   *      |          | Event 2     |         |          | Event 6     |
   *      |          | 9:30am-12pm |         |          | 9:30am-12pm |
   *      |          |             |         |          |  +-------------+
   *      |  + --------------------+         |          |  | Event 7     |
   *      +--| Event 3             |         +----------|  | 10:15am     |
   *         | 10:30am             |                    |  |             |
   *         |                     |                    +--|             |
   *         +---------------------+                       +-------------+
   * ```
   *
   * In the example above, the start time of Event 3 is more than `escapeMinutes` after the start time of
   * Event 2, therefore it is allowed to fully overlap the bottom of Event 2. As can be seen above, this does
   * not allow Event 3 to overlap the top-most overlapping event (Event 1). In the case of Event 7, the event
   * starts less than `escapeMinutes` after Event 6 and so the left edge of Event 6 remains exposed. The
   * positioning of Event 7 is determined by {@link #config-clearanceMinutes}.
   * @config {Number}
   * @default
   * @internal
   */
  escapeMinutes: null,
  /**
   * The number of pixels or proportion of the overall width to indent overlapping events that overlap by at
   * least {@link #config-clearanceMinutes}. Events that overlap by less than `clearanceMinutes` will split
   * the available space.
   *
   * Values less than 1 are the fractional proportion of the width (for example, 0.04 is 4% of the width),
   * while values greater than or equal to 1 are a number of pixels.
   * @config {Number}
   * @default
   * @internal
   */
  indentWidth: 10,
  /**
   * The maximum amount of width reduction due to overlapping items. If {@link #config-gutterWidth} is a
   * number of pixels, this value must also be in pixels (i.e., >= 1). Otherwise, this value is a proportional
   * value between 0 and 1.
   *
   * Even though this value limits the width reduction from overlapping items, it is possible that this
   * value exceeds the event width due to its nesting level (the number of prior events this event overlaps).
   * In this case, the {@link #config-staggerMinimum} value will prevent the width from becoming too small.
   * @config {Number}
   * @default
   * @internal
   */
  staggerMaximum: null,
  /**
   * This is the minimum width to which `staggerWidth` will size an event. For example, if an event has many
   * overlapping events, its width may be reduced a number of steps due to `staggerWidth`. This value limits
   * how much the event's width will be reduced.
   *
   * This value is expressed as a decimal proportion of the width of 1. For example, the value 0.4 is 40% of
   * the actual width. With this setting, the `staggerWidth` calculation will not reduce an event's width
   * below 40%.
   * @config {Number}
   * @default
   */
  staggerMinimum: 0.4,
  /**
   * When enabled, the width of an event is reduced in steps based on the number of events that overlap it.
   * Set this config to `false` or 0 to disable this effect.
   *
   * If this value is a boolean, the `gutterWidth` is used for the size of the reduction. If this value is a
   * number, it is the size of the reduction. For example, a value of 0.05 would cause an event to reduce in
   * steps of 5% width based on the number of events that overlap it. A number >= 1 is a number of pixels,
   * however, this is only valid if `gutterWidth` is also a number of pixels.
   *
   * By default, events are reduced by the `gutterWidth` based on the number of events that overlap them.
   * @config {Boolean|Number}
   * @default
   */
  staggerWidth: true,
  /**
   * Set this to `true` to use the full day width for events. By default, overlapping events equally split the
   * width.
   * @config {Boolean}
   * @default false
   */
  stretch: null
});
FluidDayLayout.initClass();
FluidDayLayout._$name = "FluidDayLayout";

// lib/Calendar/widget/DayView.js
var { MILLIS_PER_HOUR: MILLIS_PER_HOUR2 } = DayTime;
var widestDate = new Date(2020, 11, 22, 22, 59, 59);
var blockedAllDayEvents = {
  paint: 1,
  refresh: 1,
  resize: 1,
  eventspercellchange: 1,
  rangechange: 1,
  beforedatechange: 1,
  localized: 1
};
var stopEvent = (e) => {
  e.stopImmediatePropagation();
};
var preventDefault3 = (e) => e.ctrlKey && e.preventDefault();
var hourHeightLevelZero = {
  level: 0,
  step: "1 hour"
};
var zeroRect = new Rectangle(0, 0, 0, 0);
var fiveMinuteSteps = [
  "30 minutes",
  "15 minutes",
  "10 minutes",
  "5 minutes"
];
var sixMinuteSteps = [
  "30 minutes",
  "12 minutes",
  "6 minutes"
];
var extractEventRecord = (e) => e.eventRecord || e;
var emptyArray15 = Object.freeze([]);
var wrapText = (e) => typeof e === "string" ? { text: e } : e;
var DayView = class extends Panel.mixin(
  CalendarMixin_default,
  DayCellCollecter_default,
  DaySelectable_default,
  DateRangeOwner_default
) {
  static get delayable() {
    return {
      updateElementLayout: {
        type: "raf",
        cancelOutstanding: true
      },
      refreshDayBackground: {
        type: "raf",
        cancelOutstanding: true
      },
      adjustCoreHours: {
        type: "raf",
        cancelOutstanding: true
      }
    };
  }
  /**
    * A function, or name of a function which is passed the {@link DomConfig} object which
    * will be used to sync with a day cell header **in the all day row**.
    *
    * ```javascript
    * dayHeaderRenderer : function(headerDomConfig, cellData) {
    *     if (this.isSpecialDate(cellData.date)) {
    *         headerDomConfig.className['b-is-special-date'] = 1;
    *
    *         // Add to the content element's children
    *         headerDomConfig.children.push({
    *             text : 'Special day',
    *             className : 'b-special-day
    *         });
    *     }
    * }
    * ```
    *
    * The result is used to sync the DOM of the day column **in the all day row**.
    *
    * @config {Function|String} dayHeaderRenderer
    * @param {DomConfig|Object} headerDomConfig A {@link DomConfig} config object which is used to sync the day header element.
    * @param {Object} headerDomConfig.className An object who's truthy property names will be applied as class names.
    * @param {Object} headerDomConfig.style A CSS style definition object.
    * @param {Object} headerDomConfig.dataset The DOM data properties to set.
    * @param {DomConfig[]} headerDomConfig.children The {@link DomConfig} definitions the header content.
    *   There will be 2 `children` encapsulating the day name and the date.
    * @param {DayCell} cellData A `DayCell` object that contains data about the cell.
    * @returns {String}
    */
  static get configurable() {
    return {
      role: "grid",
      // have to add `timeFormat` property to `localizableProperties` even if the iterator in `Localizable` mixin
      // will be iterating over configs with `localeKey` in addition to `localizableProperties`
      // completely unclear why - the property value is localized, but rendering seem to use the old value
      localizableProperties: ["timeFormat", "timeAxisTimeFormat", "noMatchingDates"],
      layout: "vbox",
      textContent: false,
      scrollable: {
        overflowY: true,
        propagateSync: true
      },
      title: "L{Day}",
      eventHeight: "auto",
      /**
       * The height __in pixels__ of one hour cell in a day column.
       *
       * Hour cells are bounded by lines, and there are fainter subtick lines between hour lines. These subtick change
       * as zooming passes the {@link #config-hourHeightBreakpoints}.
       * @prp {Number}
       * @default
       */
      hourHeight: 42,
      /**
       * The minimum height __in pixels__ to allow a zoom request to zoom out to.
       * @prp {Number}
       * @default
       */
      minHourHeight: 15,
      range: {
        magnitude: 1,
        unit: "day"
      },
      /**
       * By default, reconfiguring the {@link #config-startDate} maintains the current duration
       * of the view and moves the {@link #config-endDate}.
       *
       * But for flexibility, reconfiguring the {@link #config-endDate} changes the duration.
       *
       * Setting `fixedDuration` to `true` locks this down to changing either end just moves
       * the view.
       *
       * This flag also signals that when using {@link Core.widget.DatePicker#config-multiSelect} in
       * the {@link Calendar.view.Calendar#config-datePicker date picker}, the selected range
       * does *not* get applied to this view.
       * @prp {Boolean}
       */
      fixedDuration: null,
      /**
       * Either the start hour of the day, or a *24 hour*&nbsp;`HH:MM` string denoting the initially visible start
       * time of the day. Defaults to 7am.
       *
       * Configure this as `null` to not have the view scroll to an initial position.
       * @default 7
       * @config {String|Number|null} [visibleStartTime=7]
       */
      visibleStartTime: {
        $config: "lazy",
        value: 7
      },
      /**
       * Either the start hour of the day, or a *24 hour*&nbsp;`HH:MM` string denoting the start time for days. This
       * is midnight by default.
       *
       * Setting this value to 12, for example, indicates that the 24 hour "day" runs from noon on one day, to
       * noon on the following day. This causes events in this span of time to layout in the same day column. In
       * this example, a two hour event that spanned midnight would be rendered in the same way a normal, 2 hour
       * event spanning noon would be rendered without this option.
       *
       * When this config is non-zero, the headings that display the day name and number are adjusted to indicate
       * the range of days for the column. For example, the "Wed" column for the 12th of the month will now show
       * "Wed-Thu" for the day name and "12-13" for the day number.
       *
       * @config {String|Number}
       * @default 0
       */
      dayStartShift: null,
      /**
       * The **configured** start time of the day, expressed in ms (8am would be represented as 8 * 60 * 60 * 1000).
       * You can set this value to either an hour value (0-23), a *24 hour*&nbsp;`HH:MM` string denoting the start of
       * the first rendered daily time block or to a ms timestamp representing time from midnight.
       *
       * Note that this is only granular to the hour level. The value will be rounded __down__
       * to the starting hour of the specified time.
       * @member {Number} dayStartTime
       */
      /**
       * Either the start hour of the day, or a *24 hour*&nbsp;`HH:MM` string denoting the start of the first rendered
       * daily time block. You can also set this value to a ms timestamp representing time from midnight.
       *
       * This config, along with {@link #config-dayEndTime} determines which hours are displayed in a day column.
       *
       * Note that this is only granular to the hour level. The value will be rounded __down__
       * to the starting hour of the configured time.
       * @config {String|Number}
       * @default 0
       */
      dayStartTime: null,
      /**
       * The **configured** end time of the day, expressed in ms (6pm would be represented as 18 * 60 * 60 * 1000).
       * You can set this value to either an hour value (1-24), a *24 hour*&nbsp;`HH:MM` string denoting the end of
       * the last rendered daily time block or to a ms timestamp representing time from midnight.
       *
       * Note that this is only granular to the hour level. The value will be rounded __up__
       * to the ending hour of the specified time.
       * @member {String|Number} dayEndTime
       */
      /**
       * Either the end hour of the day, or a *24 hour*&nbsp;`HH:MM` string denoting the end date of the last rendered time block.
       * You can also set this value to a ms timestamp representing time from midnight.
       *
       * This config, along with {@link #config-dayStartTime} determines which hours are displayed in a day column.
       *
       * Note that this is only granular to the hour level. The value will be rounded __up__
       * to the ending hour of the configured time.
       * @config {String|Number}
       * @default 24
       */
      dayEndTime: null,
      dayTime: 0,
      // ensure the change/update cycle runs using dayStart/EndTime and dayStartShift
      /**
       * A millisecond value to which to snap pointer times when clicking or dragging within a day column.
       *
       * May be specified in string form eg: `'15 minutes'`
       *
       * By default, the pointer position is __rounded__ to the nearest `increment`, but this can be
       * configured using the {@link #config-timeSnapType} option.
       * @prp {Number}
       * @accepts {String|Number}
       * @default
       */
      increment: "15 min",
      /**
       * The type of rounding to apply when calculating a date from a pointer position in a day
       * column.
       *
       * This defaults to `'round`', but may also be `'floor'` or `'ceil'`. A pointer position will
       * be snapped to the appropriate {@link #config-increment}.
       * @prp {'round'|'ceil'|'floor'}
       * @default
       */
      timeSnapType: "round",
      intradayCls: null,
      /**
       * Configure as `false` to hide the start time normally shown at the top of the events.
       *
       * Configuring as `true` (The default) means only show the start time.
       *
       * Both start and end time may be selected separately:
       *
       * ```javascript
       * {
       *     startTime : true,
       *     endTime   : true
       * }
       * ```
       *
       * @prp {Boolean|Object<'startTime'|'endTime',Boolean>}
       * @default
       */
      showTime: true,
      showBullet: false,
      iconTarget: "header",
      /**
       * Configuration to manage event layout class.
       * See {@link Calendar.layout.day.FluidDayLayout} class docs to see all possible configurations.
       *
       * Defaults to `{ type : 'fluid' }`.
       *
       * @config {FluidDayLayoutConfig}
       */
      eventLayout: {
        type: "fluid"
      },
      /**
       * The minimum width of a day column.
       *
       * If this is set, and the day columns overflow the horizontal space available, the columns
       * will be scrollable horizontally in the normal way.
       *
       * There is a {@link #property-horizontalScroller} property which handles scrolling in this
       * dimension.
       * @prp {Number|String}
       */
      minDayWidth: null,
      // In days, event colour means background color
      eventColourStyleProperty: "backgroundColor",
      monitorResize: Boolean(DomHelper.scrollBarWidth),
      // Need to adjust the width of the header
      eventFilter: null,
      /**
       * Affects drag drop and moving of events with regard to the {@link #property-increment}
       *
       * If set to `true`, startDate will be snapped relative to event's original start. e.g. if the
       * {@link #property-increment} is `15 minutes`, an event that starts at 10:03 and is dragged forwards
       * would snap its start date to 10:18, 10:33 etc.
       *
       * When set to `false` (the default), startDate will be snapped relative to the day start, eg
       * - 10:03 -> 10:15, 10:20 etc.
       *
       * @prp {Boolean}
       * @default
       */
      snapRelativeToEventStartDate: false,
      timeFormat: {
        value: "LST",
        $config: {
          localeKey: "L{timeFormat}"
        }
      },
      /**
       * A {@link Core.helper.DateHelper} format string used to format the time displayed in the
       * time axis at the left side of the view.
       *
       * @config {String}
       * @default 'LT'
       */
      timeAxisTimeFormat: {
        value: "LST",
        $config: {
          localeKey: "L{timeFormat}"
        }
      },
      /**
       * If {@link #config-showAllDayHeader} is not set to `false`, then this will be an instance
       * of {@link Calendar.widget.CalendarRow} which encapsulates the all day events at the top
       * of this view.
       * @member {Calendar.widget.CalendarRow} allDayEvents
       * @readonly
       */
      /**
       * A {@link Calendar.widget.CalendarRow} widget containing the horizontal series of calendar cells with the
       * day headers and any all-day, or day-spanning events which fall inside this view's time range.
       *
       * Note that this component calculates its height depending on its
       * {@link Calendar.widget.CalendarRow#config-eventHeight},
       * {@link Calendar.widget.CalendarRow#config-defaultEventRowCount} and
       * {@link Calendar.widget.CalendarRow#config-autoHeight} settings, therefore any configured `height`
       * and `flex` values will be ignored.
       *
       * ```javascript
       *     modes : {
       *         day : {
       *             // Do not show the whole week in the header for the one day view
       *             allDayEvents : {
       *                 fullWeek : false
       *             }
       *         }
       *     }
       * ```
       * @config {Calendar.widget.CalendarRow|CalendarRowConfig|null}
       */
      allDayEvents: {
        $config: ["lazy", "nullify"],
        value: {
          type: "calendarrow"
        }
      },
      /**
       * A config object used to create the {@link Calendar.widget.OverflowPopup} that the
       * {@link #property-allDayEvents} may show when events for one day overflow the available space.
       *
       * For example
       *
       * ```javascript
       *     modes : {
       *         week : {
       *             overflowPopup : {
       *                 closable   : false,
       *                 dateFormat : 'dddd, MMM M',
       *                 eventRenderer({ eventRecord, renderData }) {
       *                     if (calendarUtils.isImportantEvent(eventRecord)) {
       *                         // Add CSS class to important events
       *                         renderData.cls['b-important'] = 1;
       *                     }
       *                 }
       *             }
       *         }
       *     }
       * ```
       * @config {OverflowPopupConfig}
       */
      overflowPopup: null,
      /**
       * Shows an all day header above the main schedule for All Day events.
       * Set to `false` to not show all day, or multi-day events in an all day header, but
       * to have them showing wrapping through the day cells.
       * @config {Boolean}
       * @default true
       */
      showAllDayHeader: {
        value: null,
        $config: null,
        default: true
      },
      /**
       * Number of pixels to reduce the height of events by, to leave a gap between them.
       * @config {Number}
       * @default
       */
      eventSpacing: 1,
      responsive: {
        small: {
          descriptionFormat: "MMMM YYYY"
        },
        "*": {
          descriptionFormat: null
        }
      },
      /**
       * The maximum height the all day event row is allowed to grow within this view
       * when it is expanded to show all its "all day" events.
       *
       * This defaults to `50%`, but can also be set to any CSS size value. A numeric
       * value will be taken as pixels.
       * @config {Number|String}
       * @default 50%
       */
      maxAllDayHeight: null,
      /**
       * When set to `true`, the hours in the day will be sized so that they fit in the available
       * height.
       *
       * In the Object form, the value may contain `minHeight` as the minimum hour height to which
       * the hour cells may shrink:
       *
       * ```javascript
       * fitHours : {
       *     minHeight : 31
       * }
       * ```
       *
       * Note that if the all day events row at the top changes size, the space available for the
       * hours will change too, and the hour cell height will change.
       * @prp {Boolean|Object}
       * @property {Number} [minHeight=31] The minimum height in pixels to which the hour cells may shrink.
       * @default false
       */
      fitHours: null,
      /**
       * A function, or name of a function which is passed the {@link DomConfig} object which
       * will be used to sync with a day column.
       *
       * ```javascript
       * dayCellRenderer : function(domConfig, cellData) {
       *     if (this.isSpecialDate(cellData.date)) {
       *         domConfig.className['b-fa'] =
       *         domConfig.className['b-fa-birthday-cake'] = 1;
       *     }
       *     return domConfig;
       * }
       * ```
       *
       * The result is used to sync the DOM of the day column.
       *
       * @config {Function|String} dayCellRenderer
       * @param {DomConfig|Object} domConfig A {@link DomConfig} config object which is used to sync the day column element.
       * @param {Object} domConfig.className An object who's truthy property names will be applied as class names.
       * @param {Object} domConfig.style A CSS style definition object.
       * @param {Object} domConfig.dataset The DOM data properties to set.
       * @param {DomConfig[]} children The {@link DomConfig} definitions for the events in the day.
       * @param {DayCell} cellData A `DayCell` object that contains data about the cell.
       * @returns {String}
       */
      dayCellRenderer: null,
      /**
       * A function, or name of a function which yields a {@link DomConfig} object to use as a tick background element
       * for the tick context passed.
       *
       * ```javascript
       * tickRenderer({ startTime, tickIndex, subtickIndex, resourceRecord }) {
       *     // Skip subticks in the 13:00 hour
       *     if (!(startTime.getHours() === 13 && startTime.getMinutes())) {
       *         const result = {
       *             className : {
       *                 'afternoon-shift' : startTime.getHours() >= 12
       *             },
       *             text : DateHelper.format(startTime, 'hh:mm a');
       *         };
       *         // And make the 13:00 cell span the whole hour
       *         if (startTime.getHours() === 13 && !startTime.getMinutes()) {
       *             result.rowspan = subTickCount;
       *         }
       *         return result;
       *     }
       * }
       * ```
       *
       * With some application styling:
       *
       * ```CSS
       * .b-dayview-subtick {
       *     padding   : 0.5em;
       *     font-size : 0.7em;
       *     color     : #999;
       * }
       *
       * .b-dayview-subtick:hover {
       *    background-color : rgba(0, 0, 0, 0.1);
       *    cursor           : pointer;
       * }
       * ```
       *
       * This is called for every *visible* subtick. So if the view is not tall, and only hour cells are visible,
       * this will be called for every hour.
       *
       * If half hour ticks are visible too, this will be called for every half hour, if fifteen minutes ticks are
       * visible this will be called for every fifteen minutes etc.
       *
       * By default, these ticks are sized and positioned to exactly fit the subtick cell. You may use inline `style` in the
       * {@link DomConfig} return value, or a `className` property in the {@link DomConfig} return value to style the ticks.
       *
       * Returning `null` will cause no tick to be rendered for that time.
       *
       * The rendered element will have the class `b-dayview-subtick` added to it.
       *
       * If the rendered element is the last subtick in an hour, the class `b-last-in-hour` will be added.
       *
       * The rendered element's `dataset` will have the following properties:
       * - `hour` - The date of the tick.
       * - `minute` - The minute of the tick.
       * - `index` - The index of the subtick *within the hour*.
       *
       * @config {Function|String} tickRenderer
       * @param {Object} tickContext An object containing the context for the tick being rendered.
       * @param {Date} tickContext.startTime The start time of the tick.
       * @param {Date} tickContext.endTime The end time of the tick.
       * @param {Scheduler.model.EventModel[]} tickContext.events The events that visually intersect with the tick.
       * @param {Number} tickContext.tickIndex The zero-based overall tick index.
       * @param {Number} tickContext.subtickIndex The zero-based subtick index *within* the hour.
       * @param {Number} tickContext.subtickCount The total number of visible subticks in an hour.
       * @param {Scheduler.model.ResourceModel} tickContext.resourceRecord *If* this view is a resource view, the resource record.
       * @returns {DomConfig} The DOM config object to use for the tick.
       */
      tickRenderer: null,
      /**
       * An object containing two properties, `start` and `end` representing the start and
       * end of core working hours.
       *
       * This causes the non core hours to be covered by a themeable translucent grey mask
       * in the time axis.
       *
       * This may be configured to also mask the non core hours in the day part of the view
       * by setting the `overlayDay` property.
       *
       * ```javascript
       * {
       *     coreHours : {
       *         start      : 9,
       *         end        : 17,
       *         overlayDay : true
       *     }
       * }
       * ```
       *
       * If this is configured as an array of less than seven elements, it is taken to be
       * describing __multiple__ core hours blocks within one day, so it could be
       *
       * ```javascript
       * {
       *     coreHours : [{
       *         start      : 8,
       *         end        : 12,
       *     }, {
       *         start      : 14,
       *         end        : 18
       *     }
       * }
       * ```
       *
       * This may also be a __seven element__ array so as to have different core hours for
       * each JavaScript day of the week (Meaning `0` for Sunday to `6` for Saturday).
       * This causes only the hours in the day columns to be masked:
       *
       * ```javascript
       * {
       *     coreHours : [{
       *         start : 10,
       *         end   : 15
       *     },{
       *         start : 9,
       *         end   : 17
       *     },{
       *         start : 9,
       *         end   : 17
       *     },{
       *         start : 9,
       *         end   : 17
       *     },{
       *         start : 9,
       *         end   : 17
       *     },{
       *         start : 9,
       *         end   : 17
       *     },{
       *         start : 10,
       *         end   : 15
       *     }]
       * }
       * ```
       *
       * This may also be a function to return calculated core hours for every date.
       * This causes only the hours in the day columns to be masked:
       *
       * ```javascript
       * {
       *     coreHours : function(date) {
       *         // Shorter core hours at weekends
       *         if (date.getDay() === 0 || date.getDay() === 6) {
       *             return {
       *                 start : '10:00',
       *                 end   : '15:00'
       *             };
       *         }
       *         // Pre-lunch and post-lunch working blocks in the week
       *         return [{
       *             start : '08:00',
       *             end   : '12:00'
       *         }, {
       *             start : '14:00',
       *             end   : '18:00'
       *         };
       *     }
       * }
       * ```
       *
       * This may also be a __seven element__ array which contains separate core hours
       * specification for each day of the week as returned from the `Date` class.
       *
       * This may also be a function, which, when passed a `Date`, returns a core hours
       * specification for that date.
       *
       * In the simplest case an object containing two properties, `start` and `end`:
       *
       * - `coreHours.start` - The start hour or start time string `HH:MM` of the core working hours. `Date` or `String` value allowed.
       * - `coreHours.end` - The end hour or start time string `HH:MM` of the core working hours. `Date` or `String` value allowed
       * - `coreHours.overlayDay` - Set to `true` to have the greyed zone cover the day part of the view.
       *
       * __Only valid when this config is specified as a simple Object__.
       *
       * @prp {Object|Function|String} coreHours
       *
       * @param {Date} date Date for hours calculation
       * @returns {Array<Object>}
       */
      coreHours: null,
      /**
       * You can zoom in and out on the time axis using CTRL-key + mouse wheel
       * on mouse-based devices or pinch-zoom on touch devices.
       * See also the {@link #config-hourHeightBreakpoints} config option.
       *
       * You cannot zoom so far out that the day height falls below the available height.
       *
       * The rate at which zoom proceeeds can be changed by configuring this value as a number
       * which will be used as a multiplier.
       *
       * __Note that zooming necessarily sets {@link #property-fitHours} to `false`.__
       *
       * Configure this as `false` to disable this behaviour.
       * @config {Boolean|Number}
       * @default
       */
      zoomOnMouseWheel: true,
      /**
       * By default, when mousewheel zooming out from *overflowing* the client height, it is not permitted to zoom
       * further out so that the hours *underflow* the client height.
       *
       * When zooming out, the view normally stops when the hours exactly fit the client height.
       *
       * Configure this as `true` to allow mousewheel zooming out to cause the hours to underflow the client height.
       * @prp {Boolean}
       * @default
       */
      allowZoomUnderflow: false,
      /**
       * An array which encapsulates a set of {@link #config-hourHeight} breakpoints which
       * dictate when subticks - intervening time indicators - are introduced into the DayView's
       * time axis.
       *
       * Entries are in ascending granularity order, so the values must ascend.
       *
       * Subtick visibility is updated dynamically during {@link #config-zoomOnMouseWheel zooming}.
       *
       * When an {@link #property-hourHeight} change causes a change of sub tick granularity, a
       * {@link #event-tickChange} event is fired.
       *
       * @prp {Number[]}
       * @default
       */
      hourHeightBreakpoints: [70, 140, 300, 500],
      /**
       * By default, the most granular time tick level in the DayView's time axis is five minutes.
       *
       * Set this property to `true` to have the hour split into six minute ticks.
       * @prp {Boolean}
       * @default false
       */
      sixMinuteTicks: null,
      /**
       * The minimum height to which event blocks in a day cell may shrink. If an event has very
       * short duration, whatever the {@link #config-hourHeight}, and regardless of
       * {@link #function-zoomTo timeline zooming}, the event block will never drop below this height.
       * @prp {Number|String}
       * @default
       */
      minEventHeight: "1em",
      /**
       * The event block height at which the event display rendition switches to "compact"
       * mode with the name, then start time both on the top line with a slightly smaller font.
       * @prp {Number}
       * @default
       * @private
       */
      shortEventHeight: 32,
      /**
       * A function, or name of a function which produces a {@link DomConfig} block to create the current time indicator
       * which is displayed in a day column which represents the current date. It is the developer's responsibility
       * to add CSS classes and the appropriate CSS rules to produce the desire appearance.
       *
       * ```javascript
       * currentTimeIndicatorRenderer : function() {
       *     return {
       *         className : 'my-current-time',
       *         text      : 'NOW'
       *     };
       * }
       * ```
       *
       * The result is used to sync the DOM of the day column.
       *
       * @config {Function|String} currentTimeIndicatorRenderer
       * @returns {DomConfig} An object which describes the DOM structure of the today indicator element.
       */
      currentTimeIndicatorRenderer: null,
      /**
       * An object or an array of button specifications which add clickable icon buttons to
       * the rendered event blocks which contain the following properties.
       *
       * For example, the calendar may be configured like this:
       *
       * ```javascript
       * modes : {
       *     day : {
       *         actionButtons : {
       *             delete : {
       *                 tooltip : 'Delete event',
       *                 cls     : 'b-icon-trash',
       *                 handler : 'up.onDeleteAction'
       *             }
       *         }
       *     }
       * },
       *
       * onDeleteAction({ eventRecord }) {
       *     this.removeEvents([eventRecord]);
       * }
       * ```
       * @config {ActionButtonsConfig[]|Object<String,ActionButtonsConfig>}
       */
      actionButtons: null,
      /**
       * Configure this as `true` to hide day columns which contain no events.
       *
       * <div class="note">Use with care. This may result in no day columns being rendered
       * for completely empty time ranges.</div>
       * @prp {Boolean}
       * @default false
       */
      hideEmptyDays: null,
      /**
       * A callback function, or the name of a function in the ownership hierarchy which an
       * application may provide to filter out day columns for individual dates.
       * @prp {Function|String} dateFilter
       * @param {DayCell} context Information about the day column to be created.
       * @returns {Boolean} Return `false` to not display a column for the passed date.
       */
      dateFilter: null,
      autoRefresh: [
        "hideEmptyDays",
        "dateFilter"
      ],
      /**
       * By default, half hour, fifteen minute tick lines are rendered in a lower contrast
       * colour than hour lines.
       *
       * Configure this as `true` to instead render the minor tick lines as dashed lines
       * at the same contrast level as the hour lines.
       * @prp {Boolean}
       * @default false
       */
      dashedSubticks: null,
      /**
       * @typedef {DayCell} DayCellWithEvents
       * @property {Scheduler.model.EventModel[]} intraDayEvents The events that occur within a single day (not spanning multiple days). These are the events that have specific start and end times within the same day.
       * @property {Scheduler.model.EventModel[]} allEvents A combined array containing both intraDayEvents and allDayEvents. This provides a complete list of all events for the given date.
       * @property {Scheduler.model.EventModel[]} allDayEvents The events that span the entire day (all-day events). These are events that are marked as all-day events or events that span multiple days.
       */
      /**
       * A function, or name of a function which is passed the {@link DayCellWithEvents} object which
       * describes the events and details of the day.
       *
       * This function must return either an HTML string, or a {@link DomConfig} object to
       * create a column header for the passed date.
       *
       * ```javascript
       * columnHeaderRenderer({ events }) {
       *     return `${events.length ? events.length : 'No'} Events`;
       * }
       * ```
       *
       * @config {Function|String}
       * @param {DayCellWithEvents} cellData A data block describing the date and the events for that date.
       * @returns {String|Object}
       */
      columnHeaderRenderer: null,
      /**
       * By default <b>today's</b> date shows a current time line to indicate the current time. This
       * line is confined to the day cell for <b>today</b>.
       *
       * Configure this property as `false` to disable this.
       *
       * This also may have more granular options when specified as an Object:
       * @prp {Boolean|Object}
       * @property {Boolean} [fullWidth=false] When `true`, the line begins at the time axis edge
       * and covers every day that this view spans.
       * @property {Boolean} [showTime=false] When `true`, a pill showing the highlighted current time is
       * displayed at the appropriate position in the time axis.
       * @property {Boolean} [onTop=true] Set to `false`, to show the current time line <b>below</b> events.
       */
      showCurrentTime: {
        $config: "lazy",
        value: {
          onTop: true
        }
      },
      /**
       * Text or HTML, or a {@link DomConfig} block to display when all dates in the range have been filtered out.
       * @prp {String|DomConfig}
       * @default
       * @category Common
       */
      noMatchingDates: "L{noMatchingDates}",
      /**
       * By default, drag-resizing an event (including creating a new event) allows the drag gesture to span multiple day
       * columns to make the event an inter day event.
       *
       * To limit drag-resizing an event to only operating within the same day column, set this to `false`.
       *
       * Note that this setting is only valid for multi-day `DayView` instances. For other view types, *all* drag gestures
       * are inter day, so use {@link #property-allowDragResize} and {@link #property-allowDragCreate} for other views.
       * @prp {Boolean} [interDayResize=true]
       */
      interDayResize: null,
      /**
       * By default, drag-moving an event allows the drag gesture to move the event into other day columns.
       *
       * To limit drag-moving an event to only operating within the same day column, set this to `false`.
       *
       * Note that this setting is only valid for multi-day `DayView` instances. For other view types, *all* drag gestures
       * are inter day, so use {@link #property-allowDragMove} for other views.
       * @prp {Boolean} [interDayDrag=true]
       */
      interDayDrag: null,
      keyMap: {
        "+": "zoomIn",
        "Shift++": "zoomIn",
        "-": "zoomOut"
      }
    };
  }
  /**
   * Returns the resource associated with this day view when used inside a {@link Calendar.widget.ResourceView}
   * @readonly
   * @member {Scheduler.model.ResourceModel} resource
   */
  construct(config) {
    var _a5;
    const me = this;
    me.configuredHiddenVerticalScroll = ((_a5 = config.scrollable) == null ? void 0 : _a5.overflowY) === "hidden-scroll";
    me.cellMapEventFilter = me.cellMapEventFilter.bind(me);
    super.construct(...arguments);
    if (!me.startDate) {
      me.startDate = me.month.date;
    }
    GlobalEvents_default.ion({
      theme: "onThemeChange",
      thisObj: me
    });
  }
  startConfigure(config) {
    if (!this.hasConfig("fixedDuration") && !this.isDayResourceView) {
      this.fixedDuration = !(this.initialConfig.range || this.initialConfig.endDate);
    }
    super.startConfigure(config);
  }
  updateProject(project, was) {
    if (!this.isConfiguring) {
      this.allDayEvents && (this.allDayEvents.project = project);
    }
    super.updateProject(project, was);
  }
  updateZoomOnMouseWheel(zoomOnMouseWheel) {
    var _a5;
    if (zoomOnMouseWheel) {
      this.zoomListener = EventHelper.on({
        element: this.dayContentElement,
        wheel: "onDayContentMousewheel",
        thisObj: this,
        throttled: {
          buffer: 100,
          alt: preventDefault3
        }
      });
    } else {
      (_a5 = this.zoomListener) == null ? void 0 : _a5.call(this);
    }
  }
  get childItems() {
    const { allDayEvents } = this, result = super.childItems;
    if (allDayEvents) {
      result.push(allDayEvents);
    }
    return result;
  }
  get dayEndTime() {
    var _a5, _b2;
    return (_b2 = (_a5 = this.dayTime) == null ? void 0 : _a5.timeEnd) != null ? _b2 : this._dayEndTime;
  }
  get dayStartTime() {
    var _a5, _b2;
    return (_b2 = (_a5 = this.dayTime) == null ? void 0 : _a5.timeStart) != null ? _b2 : this._dayStartTime;
  }
  get dayStartShift() {
    var _a5, _b2;
    return (_b2 = (_a5 = this.dayTime) == null ? void 0 : _a5.startShift) != null ? _b2 : this._dayStartShift;
  }
  changeDayEndTime(dayEndTime) {
    this.adjustCoreHours();
    return Math.ceil((this.configuredDayEndTime = DayTime.parse(dayEndTime)) / MILLIS_PER_HOUR2) * MILLIS_PER_HOUR2;
  }
  updateDashedSubticks(dashedSubticks) {
    this.dayContainerElement.classList.toggle("b-dashed-subticks", Boolean(dashedSubticks));
  }
  updateDayEndTime(dayEndTime) {
    this._dayTime = null;
    this.dayTime = new DayTime({
      startShift: this.dayStartShift,
      timeStart: this.dayStartTime,
      timeEnd: dayEndTime
    });
  }
  changeDayStartTime(dayStartTime) {
    this.adjustCoreHours();
    return Math.floor((this.configuredDayStartTime = DayTime.parse(dayStartTime)) / MILLIS_PER_HOUR2) * MILLIS_PER_HOUR2;
  }
  adjustCoreHours() {
    if (this.initialConfig.coreHours == null) {
      const { _dayStartTime, _dayEndTime, configuredDayStartTime, configuredDayEndTime } = this;
      if (_dayStartTime < configuredDayStartTime || _dayEndTime > configuredDayEndTime) {
        this.coreHours = {
          start: configuredDayStartTime || 0,
          end: configuredDayEndTime || 24,
          overlayDay: true
        };
      } else {
        this.coreHours = null;
      }
    }
  }
  updateDate(date2, was) {
    const { allDayEvents } = this;
    if (allDayEvents) {
      allDayEvents.date = date2;
    }
    super.updateDate(date2, was);
  }
  updateDayStartTime(dayStartTime) {
    this._dayTime = null;
    this.dayTime = new DayTime({
      startShift: this.dayStartShift,
      timeStart: dayStartTime,
      timeEnd: this.dayEndTime
    });
  }
  updateDayStartShift(dayStartShift) {
    this._dayTime = null;
    this.dayTime = new DayTime({
      startShift: dayStartShift,
      timeStart: this.dayStartTime,
      timeEnd: this.dayEndTime
    });
  }
  // dayTime
  changeDayTime(dayTime) {
    var _a5;
    if (!dayTime) {
      dayTime = new DayTime(this);
    }
    if (!((_a5 = this._dayTime) == null ? void 0 : _a5.equals(dayTime))) {
      return dayTime;
    }
  }
  updateDayTime(dayTime, was) {
    var _a5;
    const me = this, { eventStore } = me;
    if (eventStore) {
      eventStore.registerDayIndex(dayTime);
      was && eventStore.unregisterDayIndex(was);
    }
    if (!me.isConfiguring) {
      me.syncHours();
      if (dayTime.startShift !== (was == null ? void 0 : was.startShift)) {
        const startDate = dayTime.startOfDay(me.startDate), date2 = dayTime.startOfDay(me.date);
        me.setConfig({
          date: date2,
          startDate
        });
        (_a5 = me.allDayEvents) == null ? void 0 : _a5.setConfig({
          date: date2,
          dayTime,
          startDate
        });
      }
    }
  }
  dateKey(date2) {
    return this.dayTime.dateKey(date2);
  }
  dayOfDate(date2) {
    return date2 && this.dayTime.dayOfDate(date2);
  }
  /**
   * Scrolls vertically to bring an event or a time into view.
   * @param {Scheduler.model.EventModel|Date|Number} target The event to scroll to or
   * a `Date` to read the hour value from, or an hour number.
   * @param {Object} [options] How to scroll.
   * @param {'start'|'end'|'center'|'nearest'} [options.block] How far to scroll the target.
   * @param {Number} [options.edgeOffset] edgeOffset A margin around the target to bring into view.
   * @param {Object|Boolean|Number} [options.animate] Set to `true` to animate the scroll by 300ms,
   * or the number of milliseconds to animate over, or an animation config object.
   * @param {Number} [options.animate.duration] The number of milliseconds to animate over.
   * @param {String} [options.animate.easing] The name of an easing function.
   * @param {Boolean} [options.highlight] Set to `true` to highlight the target when it is in view, _if the target is an `EventModel`_.
   * @param {Boolean} [options.focus] Set to `true` to focus the target when it is in view.
   * @param {Boolean} [options.x] Pass as `false` to disable scrolling in the `X` axis.
   * @param {Boolean} [options.y] Pass as `false` to disable scrolling in the `Y` axis.
   * @returns {Promise} A promise which is resolved when the target has been scrolled into view.
   */
  async scrollTo(target, options) {
    const me = this, { allDayEvents } = me, scrollPromises = [], hourPassed = typeof target === "number", targetDate = hourPassed ? DateHelper.add(me.startDate, target, "hour") : typeof target === "string" ? DateHelper.parse(target) : new Date(target), targetIsDate = !isNaN(targetDate);
    await me.scrollPromise;
    await (allDayEvents == null ? void 0 : allDayEvents.heightAnimation);
    if (me.isDestroyed) {
      return;
    }
    options = ObjectHelper.assign({}, me.defaultScrollOptions, typeof options == "boolean" ? { animate: options } : options);
    scrollPromises.push(await super.scrollTo(targetIsDate ? targetDate : target, { ...options, y: !targetIsDate, highlight: false }));
    let scrollTarget;
    if (targetIsDate) {
      const { firstVisibleDate, lastVisibleDate } = me;
      firstVisibleDate.setHours(me.dayStartHour);
      lastVisibleDate.setHours(me.dayEndHour);
      targetDate.setTime(DateHelper.clamp(targetDate, firstVisibleDate, lastVisibleDate));
      let incr = -1;
      while (!me.getDayElement(targetDate)) {
        if (targetDate < firstVisibleDate || targetDate > lastVisibleDate) {
          incr = -incr;
          if (incr < 0) {
            return;
          }
        }
        targetDate.setDate(targetDate.getDate() + incr);
      }
      scrollTarget = me.getDayElement(targetDate);
      if (scrollTarget) {
        if (options.y !== false) {
          target = targetDate.getHours() + targetDate.getMinutes() / 60;
          scrollPromises.push(me.scrollable.scrollTo(null, me.getPositionFromTime(target), options));
        }
      }
    } else {
      scrollTarget = me.getEventElement(target);
      if (scrollTarget) {
        const cellRect = Rectangle.from(scrollTarget.closest(".b-calendar-cell")), eventRect = Rectangle.from(scrollTarget);
        options.edgeOffset = {
          end: me.rtl ? eventRect.x - cellRect.x : cellRect.right - eventRect.right
        };
      }
    }
    if (scrollTarget && (!targetIsDate || me.duration > 1 && !hourPassed)) {
      scrollPromises.push(me.horizontalScroller.scrollIntoView(scrollTarget, options));
    }
    return me.scrollPromise = Promise.all(scrollPromises).then(() => me.scrollPromise = null);
  }
  getEventElement(event, date2) {
    var _a5;
    return super.getEventElement(event, date2) || ((_a5 = this.allDayEvents) == null ? void 0 : _a5.getEventElement(event, date2));
  }
  getEventElements(event) {
    var _a5;
    return super.getEventElements(event) || ((_a5 = this.allDayEvents) == null ? void 0 : _a5.getEventElements(event));
  }
  /**
   * Returns the pixel coordinate on the time axis for the passed time of day.
   * @param {Date|Number|String} time The hour number, 'HH:MM' time or a `Date` instance.
   * @internal
   * @returns {Number}
   */
  getPositionFromTime(time) {
    if (DayTime.parse(time) < DayTime.parse(this.dayStartTime) - this.dayStartShift) {
      return 0;
    }
    return this.dayTime.delta(time, "h") * this.hourHeight;
  }
  updateShowAllDayHeader(showAllDayHeader) {
    var _a5, _b2;
    const me = this, { _allDayEvents } = me;
    (_a5 = me._cellMap) == null ? void 0 : _a5.clear();
    me.element.classList.toggle("b-hide-allday-header", !showAllDayHeader);
    if (_allDayEvents) {
      (_b2 = _allDayEvents._cellMap) == null ? void 0 : _b2.clear();
      _allDayEvents.element.classList.toggle("b-hide-allday-header", !showAllDayHeader);
      _allDayEvents.refresh();
    }
    if (!me.isConfiguring) {
      me.refresh();
    }
  }
  changeAllDayEvents(alldayEvents, oldAlldayEvents) {
    var _a5;
    if (!alldayEvents) {
      return oldAlldayEvents == null ? void 0 : oldAlldayEvents.destroy();
    }
    this.getConfig("date");
    if (alldayEvents.maxHeight) {
      this.maxAllDayHeight = alldayEvents.maxHeight;
      alldayEvents.maxHeight = null;
    }
    const me = this, {
      autoCreate,
      dayHeaderRenderer,
      overflowButtonRenderer,
      overflowClickAction,
      overflowPopup,
      avatarRendering,
      range,
      shiftIncrement
    } = me, showAllDayHeader = me.isConfiguring ? me.peekConfig("showAllDayHeader") : me.showAllDayHeader, result = CalendarRow.reconfigure(oldAlldayEvents, alldayEvents, {
      owner: me,
      defaults: {
        calendar: me.calendar,
        parent: me,
        rtl: me.rtl,
        cls: {
          "b-dayview-schedule-container": 1,
          "b-dayview-initializing": 1,
          "b-hide-allday-header": !showAllDayHeader
        },
        // If we were configured with any of the following, they were obviously intended for use
        // by the all day row
        [overflowPopup !== void 0 ? "overflowPopup" : "_"]: overflowPopup,
        [overflowButtonRenderer ? "overflowButtonRenderer" : "_"]: overflowButtonRenderer,
        [overflowClickAction ? "overflowClickAction" : "_"]: overflowClickAction,
        [dayHeaderRenderer ? "dayHeaderRenderer" : "_"]: dayHeaderRenderer,
        [avatarRendering ? "avatarRendering" : "_"]: avatarRendering,
        [shiftIncrement ? "shiftIncrement" : "_"]: shiftIncrement,
        // This view always autoCreates allDay events starting at midnight
        autoCreate: autoCreate !== false ? {
          newName: autoCreate.newName,
          gesture: autoCreate.gesture,
          startHour: 0,
          // If this DayView was configured with an autoCreate duration, use the durationUnit
          // specified there as the durationUnit for the allDayEvents.
          // If we are in the default configuration, use a duration of 1 day.
          duration: ((_a5 = me.initialConfig.autoCreate) == null ? void 0 : _a5.duration) ? `${DateHelper.as(autoCreate.durationUnit, "1 day")} ${autoCreate.durationUnit}` : "1 day"
        } : false,
        // Pass on any non-default dragUnit configuration
        [me.initialConfig.dragUnit ? "dragUnit" : "_"]: me.dragUnit,
        // Only signal a dayNumberClick on a click on the day number element.
        // Allow clicks in the surrounding header to be used to create new events.
        dayNameSelector: ".b-day-name-date",
        // Must use the same configured event filter as us in addition to only showing
        // the all day and interDay events
        eventFilter: (e) => me.showAllDayHeader && me.isAllDayEvent(e) && (!me.eventFilter || me.eventFilter(e)),
        dayStartTime: me.dayStartTime,
        dayEndTime: me.dayEndTime,
        dayStartShift: me.dayStartShift,
        eventRenderer: me.eventRenderer,
        eventStore: me.eventStore,
        month: me.month,
        // Only upgrade to show a full week header if we are the fixed duration default day mode
        // Not if we are a variable duration DayView which may be controlled via the datePicker
        fullWeek: me.duration === 1 && me.fixedDuration && !me.dayTime.startShift,
        filterEventResources: me.filterEventResources,
        startDate: me.startDate,
        [range ? "range" : "_"]: range,
        [range ? "_" : "endDate"]: me.endDate,
        nonWorkingDays: me.nonWorkingDays,
        dateFilter: me.dateFilter,
        nonWorkingDayCls: me.nonWorkingDayCls,
        weekendCls: me.weekendCls,
        weekStartDay: me.weekStartDay,
        defaultCalendar: me.defaultCalendar,
        emptyCellRenderer: me.emptyCellRenderer,
        hideEmptyDays: me.hideEmptyDays,
        syncCalendarDate: me.syncCalendarDate,
        internalListeners: {
          heightChange: "updateElementLayout",
          refresh: "updateElementLayout",
          catchall: "relayAllDayEvents",
          showoverflowpopup: "onAllDayOverflowPopupShown",
          thisObj: me
        },
        // If all day row does not render events, the day part must fire
        // the creation signal so that responders such as EventEdit can do right things.
        editAutoCreatedEvent(eventRecord) {
          (me.showAllDayHeader ? this : me).trigger("eventAutoCreated", {
            eventRecord
          });
        },
        // If we are not showing all day events, always return an empty Map
        createCellMap() {
          return me.showAllDayHeader ? this.constructor.prototype.createCellMap.call(this, ...arguments) : me.emptyMap || (me.emptyMap = /* @__PURE__ */ new Map());
        },
        actionButtons: me.actionButtons
      },
      setup(config) {
        config.hideNonWorkingDays = me.duration === 1 && !config.fullWeek ? false : me._hideNonWorkingDays;
      }
    });
    if (result) {
      result.height = result.flex = null;
      me.originalAllDayEventsScrollTo = result.scrollTo;
      result.scrollTo = me.allDayEventsScrollTo.bind(me);
    }
    return result;
  }
  // We have to be able to yield the underbar property so that Calendar can ask if there is an
  // overflowPopup for a child view without triggering its creation.
  onAllDayOverflowPopupShown({ overflowPopup }) {
    this._overflowPopup = overflowPopup;
  }
  // This is what gets called when the all day row is asked to scrollTo
  // We have control of what it does. It does not have control of its date range.
  allDayEventsScrollTo(target) {
    const me = this, newDate = target.isEvent ? target.startDate : me.changeEndDate(target), dateIntersects = target.isEvent ? DateHelper.intersectSpans(me.startDate, me.endDate, target.startDate, target.endDate) : DateHelper.betweenLesser(newDate, me.startDate, me.endDate);
    if (!dateIntersects) {
      me.date = newDate;
    } else {
      return me.originalAllDayEventsScrollTo.call(me.allDayEvents, ...arguments);
    }
  }
  updateAutoCreate(autoCreate) {
    var _a5;
    const { _allDayEvents } = this;
    (_a5 = super.updateAutoCreate) == null ? void 0 : _a5.call(this, autoCreate);
    if (_allDayEvents) {
      _allDayEvents.autoCreate = autoCreate ? {
        newName: autoCreate.newName,
        gesture: autoCreate.gesture,
        startHour: 0,
        duration: "1 day"
      } : false;
    }
  }
  updateWeekStartDay(weekStartDay, oldWeekStartDay) {
    var _a5;
    (_a5 = super.updateWeekStartDay) == null ? void 0 : _a5.call(this, weekStartDay);
    if (!this.isConfiguring && this.allDayEvents) {
      this.allDayEvents.weekStartDay = weekStartDay;
    }
  }
  updateEventStore(eventStore, was) {
    super.updateEventStore(eventStore, was);
    const { dayTime } = this;
    if (dayTime) {
      eventStore == null ? void 0 : eventStore.registerDayIndex(dayTime);
      was == null ? void 0 : was.unregisterDayIndex(dayTime);
    }
  }
  /**
   * The {@link Calendar.widget.OverflowPopup} instance that the {@link #property-allDayEvents}
   * may show when events for one day overflow the available space.
   * @member {Calendar.widget.OverflowPopup} overflowPopup
   * @readonly
   */
  get overflowPopup() {
    var _a5;
    return ((_a5 = this._allDayEvents) == null ? void 0 : _a5.overflowPopup) || this._overflowPopup;
  }
  get description() {
    const me = this, {
      range,
      shortDateFormat
    } = me;
    if (me.descriptionRenderer || me.fixedDuration || me.duration === 1 || (range == null ? void 0 : range.magnitude) === 1 && range.unit === "day") {
      return super.description;
    }
    if (range && !me.dateFilter) {
      return `${DateHelper.format(me.startDate, shortDateFormat)}, ${range.magnitude} ${DateHelper.getLocalizedNameOfUnit(range.unit, range.magnitude > 1)}`;
    } else {
      return `${DateHelper.format(me.startDate, shortDateFormat)} - ${DateHelper.format(DateHelper.add(me.endDate, -1, "d"), shortDateFormat)}`;
    }
  }
  get eventContentElement() {
    return this.dayContainerElement;
  }
  getDateFromDomEvent(domEvent, precise = false) {
    let date2 = super.getDateFromDomEvent(domEvent);
    const me = this, el = DomHelper.getEventElement(domEvent);
    if (date2 && el !== domEvent && me.dayContainerElement.contains(el)) {
      const y = (domEvent.clientY || el.getBoundingClientRect().y) - 0.5;
      date2 = DateHelper.add(
        date2,
        me.dayStartHour - me.dayStartShift / 36e5 + (y - me.scrollable.element.getBoundingClientRect().y + me.scrollable.y) / me.hourHeight,
        "hour"
      );
      if (!precise) {
        date2 = DateHelper[me.timeSnapType](date2, me.increment);
      }
    }
    return date2;
  }
  getDateFromPosition(clientX, clientY, local = false, keyParser) {
    const me = this, {
      eventContentElement,
      allDayEvents
    } = me, rect = local ? zeroRect : Rectangle.fromScreen(eventContentElement).roundPx().translate(-eventContentElement.scrollLeft), width = eventContentElement.scrollWidth, localX = local ? clientX : clientX - rect.x, dx = me.rtl ? me.eventContentElement.offsetWidth - localX : localX, dy = clientY - rect.y, overCell = me.getDayElementFromX(clientX), height = (overCell == null ? void 0 : overCell.offsetHeight) || me.hourHeight * me.getDayLength("hour"), date2 = me.getDateFromElement(overCell, keyParser) || me.date;
    if (allDayEvents && (dx < 0 || width < dx || dy < 0 || height < dy)) {
      return allDayEvents.getDateFromPosition(clientX, clientY, keyParser);
    }
    DateHelper.clearTime(
      date2,
      /*clone = */
      false
    );
    date2.setMilliseconds(date2.getMilliseconds() + me.dayStartMs + Math.floor(dy / height * me.getDayLength()));
    return DateHelper[me.timeSnapType](date2, me.increment);
  }
  // We must implement the CalendarMixin interface.
  // All views must expose a doRefresh method.
  doRefresh() {
    var _a5, _b2, _c2, _d;
    const me = this, {
      element,
      noMatchingDates,
      allDayEvents
    } = me, columnHeaderRenderer = me.resolveCallback(me.columnHeaderRenderer, me, false);
    if (me.isVisible) {
      if (!me.allowTransitionsOnRefresh) {
        DomHelper.addTemporaryClass(element, "b-no-transitions", 200, me);
      }
      me.allowTransitionsOnRefresh = null;
      const headerCells = [], dayCells = [];
      me.today = me.dayTime.startOfDay(((_a5 = me.calendar) == null ? void 0 : _a5.dateTimeNow) || /* @__PURE__ */ new Date());
      for (let i = 0, date2 = new Date(me.startDate); date2 < me.endDate; i++, date2.setDate(date2.getDate() + 1)) {
        const dayCell = me.getDayDomConfig(date2);
        if (dayCell) {
          dayCells.push(dayCell);
          if (columnHeaderRenderer) {
            let allDay = [], allEvents = [], cellData;
            const key = me.dayTime.dateKey(date2);
            if (allDayEvents) {
              allDay = ((_b2 = allDayEvents.cellMap.get(key)) == null ? void 0 : _b2.events) ? allDayEvents.cellMap.get(key).events.map(({ eventRecord }) => eventRecord) : [];
            }
            const headerCell = {
              dataset: {
                syncId: key
              },
              className: {
                "b-day-column-header-cell": 1
              }
            }, intraDayEvents = ((_c2 = me.cellMap.get(key)) == null ? void 0 : _c2.events) || [];
            allEvents = [...intraDayEvents, ...allDay];
            cellData = Object.assign(me.createCellData(date2), {
              events: intraDayEvents,
              allDayEvents: allDay,
              intraDayEvents,
              allEvents
            });
            const headerContent = columnHeaderRenderer.handler.call(columnHeaderRenderer.thisObj, cellData);
            if (headerContent) {
              headerCell[typeof headerContent === "string" ? "html" : "children"] = headerContent;
            }
            headerCells.push(headerCell);
          }
        }
      }
      if (columnHeaderRenderer) {
        element.classList.add("b-has-day-header");
        DomSync.sync({
          targetElement: me.dayHeaderContentElement,
          domConfig: {
            onlyChildren: true,
            children: headerCells,
            // Match existing data-syncId elements first and ensure DOM order matches
            // children order.
            syncOptions: {
              syncIdField: "syncId",
              releaseThreshold: 0,
              strict: true
            }
          }
        });
      } else {
        element.classList.remove("b-has-day-header");
      }
      element.classList.toggle("b-multidayview", dayCells.length > 1);
      element.style.setProperty("--visible-column-count", dayCells.length);
      (_d = me.onDayCellConfigGenerated) == null ? void 0 : _d.call(me, dayCells);
      DomSync.sync({
        targetElement: me.dayContainerElement,
        domConfig: {
          onlyChildren: true,
          children: dayCells.length ? dayCells : [{
            className: "b-empty-text",
            [(noMatchingDates == null ? void 0 : noMatchingDates.includes("<")) ? "html" : "text"]: noMatchingDates
          }],
          // Match existing data-date elements first and ensure DOM order matches
          // children order.
          syncOptions: {
            syncIdField: "date",
            releaseThreshold: 0,
            strict: dayCells.length > 0
          }
        }
      });
      me.element.classList.toggle("b-no-days", !dayCells.length);
      me.refreshDayBackground();
      me.refreshCount = (me.refreshCount || 0) + 1;
      me.syncCurrentTimeIndicators();
      me.trigger("refresh");
    }
  }
  /**
   * Gets the full event load which the passed date contains for this view. Includes events
   * in the all day row as well as in the main day area.
   * @param {Date|String} date The date or `YYYY-MM-DD` key for which to gather events.
   * @param {Date} startDate To sync this view's range with the caller's range. Used by
   * this view's {@link #property-allDayEvents} view.
   * @returns {Scheduler.model.EventModel[]}
   * @private
   */
  getEventsForDay(date2, startDate) {
    var _a5, _b2, _c2, _d, _e;
    const me = this, key = typeof date2 === "string" ? date2 : me.dayTime.dateKey(date2), { allDayEvents } = me;
    if (startDate && me.startDate - startDate) {
      (_a5 = me._cellMap) == null ? void 0 : _a5.clear();
      me.startDate = startDate;
    }
    const result = ((_c2 = (_b2 = me.cellMap.get(key)) == null ? void 0 : _b2.events) == null ? void 0 : _c2.slice()) || [];
    if (allDayEvents) {
      if (allDayEvents.startDate - me.startDate) {
        (_d = allDayEvents._cellMap) == null ? void 0 : _d.clear();
        allDayEvents.startDate = me.startDate;
      }
      result.push.apply(result, ((_e = allDayEvents.cellMap.get(key)) == null ? void 0 : _e.renderedEvents) || emptyArray15);
    }
    return result.map(extractEventRecord);
  }
  // Override from CalendarMixin.
  // DayView's "today" may be shifted by the dayStartShift.
  // So when dayStartShift is 18, days start at 6pm, so today is 6pm today.
  // this.today is set at the start of doRefresh() when we know the current date.
  isToday(date2) {
    return !Boolean(date2 - this.today);
  }
  getBaseDayDomConfig(date2) {
    var _a5;
    const me = this, {
      dateFilter,
      resource,
      today
    } = me, key = me.dayTime.dateKey(date2), cellData = me.cellMap.get(key) || me.cellMap.set(key, me.createCellData(date2)).get(key), { day: day2 } = cellData, skipDay = me.hiddenNonWorkingDays[day2] && me.duration !== 1 || me.hideEmptyDays && !me.getEventsForDay(key).length || dateFilter && !me.callback(dateFilter, me, [cellData]);
    if (!skipDay) {
      const nonWorkingDays = me.nonWorkingDays || me.month.nonWorkingDays, isToday = !(date2 - today), todayText = isToday ? me.L("L{Object.today}, ") : "";
      return [cellData, {
        tabIndex: -1,
        role: "gridcell",
        "aria-label": `${todayText}${DateHelper.format(date2, "dddd Do MMMM")}${resource ? ` ${resource.name}` : ""}, ${me.L("eventCount", ((_a5 = cellData.events) == null ? void 0 : _a5.length) || 0)}.`,
        dataset: {
          date: key
        },
        className: {
          "b-dayview-day-detail": 1,
          [me.dayCellCls]: 1,
          [me.nonWorkingDayCls]: nonWorkingDays[day2] || false,
          [me.weekendCls]: DateHelper.weekends[day2],
          [me.todayCls]: isToday,
          "b-last-cell": !(date2 - me.lastVisibleDate),
          [`b-day-of-week-${day2}`]: 1,
          "b-past-date": date2 < today
        },
        style: {},
        children: {
          inset: {
            className: {
              "b-dayview-inset": 1
            }
          },
          events: {
            className: {
              "b-dayview-event-container": 1
            },
            children: []
          },
          ...me.renderTickBackground(cellData, resource)
        },
        // Match existing data-event-id elements first and ensure DOM order matches
        // children order.
        syncOptions: {
          syncIdField: "eventId",
          releaseThreshold: 0,
          strict: true
        }
      }];
    }
  }
  renderTickBackground(cellData, resourceRecord) {
    const me = this, tickRenderer = me.resolveCallback(me.tickRenderer, me, false), result = {};
    if (tickRenderer) {
      const subTickCount = parseInt(DomHelper.getStyleValue(me.contentElement, "--subtick-count")), dayLength = me.getDayLength("h"), tickCount = dayLength * subTickCount, tickDuration = dayLength / tickCount, { dayTime } = me, { startHour } = dayTime;
      let subtick, date2 = new Date(cellData.date), tickIndex, subtickIndex;
      date2.setHours(startHour);
      for (tickIndex = 0, subtickIndex = 0; tickIndex < tickCount; tickIndex++) {
        const tickDate = date2;
        date2 = DateHelper.add(date2, tickDuration, "h");
        subtick = tickRenderer.handler.call(tickRenderer.thisObj, {
          events: cellData.events.filter((e) => DateHelper.intersectSpans(tickDate, date2, e.startDate, e.endingDate)),
          startTime: tickDate,
          endTime: date2,
          tickIndex,
          subtickIndex,
          subTickCount,
          resourceRecord
        });
        if (subtick) {
          if (typeof subtick === "string") {
            subtick = {
              text: subtick
            };
          } else if (Array.isArray(subtick)) {
            subtick = {
              children: subtick.map(wrapText)
            };
          }
          const isLastInHour = !date2.getMinutes(), rowspan = subtick.rowspan || subtick.rowSpan;
          if (typeof subtick.style === "string") {
            subtick.style = DomHelper.parseStyle(subtick.style);
          }
          (subtick.style || (subtick.style = {}))["--tick-index"] = tickIndex;
          if (rowspan) {
            subtick.style["--tick-rowspan"] = rowspan;
            subtick.rowspan = subtick.rowSpan = null;
          }
          subtick.className = {
            ...DomClassList.from(subtick.className, true),
            "b-dayview-subtick": 1,
            "b-last-in-hour": isLastInHour
          };
          (subtick.dataset || (subtick.dataset = {})).hour = tickDate.getHours();
          subtick.dataset.minute = tickDate.getMinutes();
          subtick.dataset.index = subtickIndex++;
          if (isLastInHour) {
            subtickIndex = 0;
          }
          result[`subtick-${tickIndex}`] = subtick;
        }
      }
    }
    return result;
  }
  getDayDomConfig(date2) {
    const me = this, base = me.getBaseDayDomConfig(date2);
    if (base) {
      const { dayCellRenderer } = me, [cellData, dayDomConfig] = base;
      if (me.coreHours) {
        dayDomConfig.style.backgroundImage = me.getCoreHoursGradient(me.getCoreHoursForDate(date2));
      }
      me.renderEvents(cellData, dayDomConfig);
      return DomHelper.normalizeChildren(
        dayCellRenderer ? me.callback(dayCellRenderer, me, [dayDomConfig, cellData]) : dayDomConfig
      );
    }
  }
  onCalendarKeyDown(keyEvent) {
    var _a5, _b2, _c2, _d;
    const { target } = keyEvent, {
      dayCellCls,
      allDayEvents
    } = this, {
      eventContentElement
    } = ((_a5 = this.owner) == null ? void 0 : _a5.isCalendarMixin) ? this.owner : this;
    if (eventContentElement.contains(target)) {
      const eventCellSelector = `${target.matches(".b-dayview-day-detail") ? ".b-dayview-day-detail" : ""}.${dayCellCls}`, from = target.closest(eventCellSelector) || target, date2 = (_b2 = from.closest("[data-date]")) == null ? void 0 : _b2.dataset.date, eventCellFocused = from.matches(eventCellSelector), { resourceId } = from.dataset, resourceSelector = resourceId ? ` [data-resource-id="${resourceId}"]` : "";
      switch (keyEvent.key) {
        case "ArrowUp":
          if (eventCellFocused && allDayEvents) {
            keyEvent.preventDefault();
            let to = allDayEvents.eventContentElement.querySelector(`.${allDayEvents.dayCellCls}[data-date="${date2}"]${resourceSelector}`);
            if (!DomHelper.isFocusable(to) || !DomHelper.isInView(to)) {
              to = allDayEvents.headerCellContainer.querySelector(`[data-header-date="${date2}"]`).focus();
            }
            to == null ? void 0 : to.focus();
          }
          return;
        case "ArrowLeft":
          keyEvent.preventDefault();
          (_c2 = ElementVisitor.getVisitor(eventContentElement, eventCellSelector).previous(from)) == null ? void 0 : _c2.focus();
          return;
        case "ArrowRight":
          keyEvent.preventDefault();
          (_d = ElementVisitor.getVisitor(eventContentElement, eventCellSelector).next(from)) == null ? void 0 : _d.focus();
          return;
      }
      return super.onCalendarKeyDown(keyEvent);
    }
  }
  /**
   * Calculates A CSS `linear-gradient` specification from a {@link #config-coreHours} definition object.
   *
   * A core hours object consists ot two properties, `start` and `end`. It may also be an array containing
   * multiple core hours definitions.
   * @param {Object} coreHours
   * @param {Number|String} coreHours.start The start hour or start time string `HH:MM` of the core working hours.
   * @param {Number|String} coreHours.end The start hour or end time string `HH:MM` of the core working hours.
   * @returns A String containing a `linear-gradient` style which shades non-working times using the
   * `--dayview-outside-core-hours-color` CSS variable.
   * @internal
   */
  getCoreHoursGradient(coreHours) {
    if (!coreHours) {
      return "";
    }
    const me = this, { dayTime } = me, dayEnd = dayTime.duration("h"), result = ["linear-gradient(to bottom"];
    coreHours = ArrayHelper.asArray(coreHours);
    let lastEnd = 0;
    for (let i = 0, { length } = coreHours; i < length; i++) {
      const c = coreHours[i], start = Math.max(dayTime.delta(c.start, "h"), 0) / dayEnd * 100, end = Math.min(dayTime.delta(c.end, "h"), dayEnd) / dayEnd * 100;
      if (length === 1 && start === 0 && end === 100) {
        return "";
      }
      if (lastEnd < start) {
        result.push(`var(--dayview-outside-core-hours-color) ${DomHelper.percentify(lastEnd)}, var(--dayview-outside-core-hours-color) ${DomHelper.percentify(start)}`);
      }
      result.push(`transparent ${DomHelper.percentify(start)}, transparent ${DomHelper.percentify(end)}`);
      lastEnd = end;
    }
    if (lastEnd < 100) {
      result.push(`var(--dayview-outside-core-hours-color) ${DomHelper.percentify(lastEnd)}, var(--dayview-outside-core-hours-color) 100%`);
    }
    return result.join(",") + ")";
  }
  getCoreHoursForDate(date2) {
    const { coreHours } = this;
    if (coreHours.length === 7) {
      return coreHours[date2.getDay()];
    }
    if (typeof coreHours === "object") {
      return coreHours.overlayDay ? coreHours : null;
    }
    return this.callback(coreHours, this, [date2]);
  }
  /**
   * Refreshes the event layout inside a day column in case the stacking order becomes incorrect.
   * @param {Date} date
   * @internal
   */
  refreshDayEvents(date2) {
    const me = this;
    date2 = me.changeEndDate(date2);
    me.today = me.dayTime.startOfDay(/* @__PURE__ */ new Date());
    const domConfig = me.getDayDomConfig(date2);
    DomHelper.addTemporaryClass(me.element, "b-no-transitions", 200, me);
    domConfig.onlyChildren = true;
    DomSync.sync({
      targetElement: me.getDayElement(date2),
      domConfig
    });
  }
  /**
   * Pushes event bar DomConfig definitions into the `children` property of the passed
   * `dayDomConfig` object
   * @param {DayCell} cellData An object containing information about the day cell being created.
   * @param {DomConfig} dayDomConfig The DomConfig element definition for the day cell.
   * @internal
   */
  renderEvents(cellData, dayDomConfig) {
    const me = this, context = me.eventLayout.layoutEvents(cellData, dayDomConfig), { items: items2 } = context, { eventSpacing, rtl } = me;
    for (const item of items2) {
      const eventDomConfig = me.createEventDomConfig({ date: cellData.date, eventRecord: item.eventRecord });
      me.insertActionButtons(eventDomConfig.children[0].children[0].children, item.eventRecord, cellData);
      eventDomConfig.className["b-starts-above"] = item.startsBefore;
      eventDomConfig.className["b-ends-below"] = item.endsAfter;
      if (item.cluster.items.length > 1) {
        eventDomConfig.className["b-cal-in-cluster"] = 1;
      }
      Object.assign(eventDomConfig.style, {
        // Leave eventSpacing pixels at the bottom by reducing the available content height.
        // The event-wrap's default background-color is transparent.
        // This is to create the appearance of the event-spacing config.
        [eventSpacing ? "padding-bottom" : ""]: `${eventSpacing}px`,
        ...item.getStyles(rtl)
      });
      dayDomConfig.children.events.children.push(eventDomConfig);
    }
    me.trigger("renderEvents", {
      context
    });
  }
  changeActionButtons(actionButtons) {
    const arrayPassed = Array.isArray(actionButtons);
    if (arrayPassed) {
      actionButtons.forEach((b, i) => {
        if (!("ref" in b)) {
          b.ref = `action-button-${i + 1}`;
        }
      });
    }
    this._actionButtonsObject = arrayPassed ? ObjectHelper.transformArrayToNamedObject(actionButtons) : actionButtons;
    let value2;
    return actionButtons = new Proxy(actionButtons, {
      deleteProperty(target, prop) {
        return delete (value2 || (value2 = ObjectHelper.clone(target)))[prop];
      },
      get(target, prop) {
        if (prop === "$value") {
          return value2 || target;
        } else if (prop === "$reset") {
          return () => value2 = null;
        } else if (Array.isArray(target) && typeof target[prop] === "function") {
          return (value2 || (value2 = ObjectHelper.clone(target)))[prop].bind(value2);
        }
        return Reflect.get(...arguments);
      },
      set(target, prop, newValue) {
        return Reflect.set(value2 || (value2 = ObjectHelper.clone(target)), prop, newValue);
      }
    });
  }
  insertActionButtons(children, eventRecord, cellData) {
    const { actionButtons } = this;
    if (actionButtons) {
      const { $value: buttons } = actionButtons, buttonConfigs = Array.isArray(buttons) ? buttons : ObjectHelper.transformNamedObjectToArray(buttons);
      if (buttonConfigs.length) {
        children.push({
          className: "b-event-action-buttons",
          children: buttonConfigs.sort(Widget.weightSortFn).map(({ cls, tooltip, ref }, index) => {
            return {
              tag: "button",
              className: {
                "b-tool": 1,
                "b-icon": 1,
                [cls]: 1
              },
              dataset: {
                ref,
                btip: tooltip
              },
              "aria-label": tooltip
            };
          })
        });
      }
      actionButtons.$reset();
    }
  }
  onEventClick({ domEvent }) {
    const button = domEvent.target.closest(".b-event-action-buttons button[data-ref]");
    if (button) {
      this.callback(this._actionButtonsObject[button.dataset.ref].handler, this, arguments);
      return false;
    }
  }
  get overflowElement() {
    return this.dayContentElement;
  }
  get cellMap() {
    var _a5;
    return ((_a5 = this._cellMap) == null ? void 0 : _a5.populated) ? this._cellMap : this.createCellMap();
  }
  get bodyConfig() {
    const me = this, {
      allDayEvents,
      maxAllDayHeight
    } = me;
    return Object.assign(super.bodyConfig, {
      children: {
        alldayRowElement: allDayEvents ? {
          className: {
            "b-dayview-allday-row": 1
          },
          style: {
            [maxAllDayHeight ? "maxHeight" : ""]: DomHelper.setLength(maxAllDayHeight)
          },
          children: {
            cornerElement: {
              className: {
                "b-dayview-allday-row-start": 1,
                "b-dayview-allday-autoheight": allDayEvents.autoHeight
              },
              children: {
                allDayTextElement: {
                  className: "b-dayview-allday-text",
                  html: me.L("L{EventEdit.All day}")
                },
                allDayToggleIcon: {
                  tag: "button",
                  className: "b-expand-allday-button b-icon b-icon-expand-row",
                  dataset: {
                    btip: me.L("L{DayView.expandAllDayRow}")
                  },
                  "aria-label": me.L("L{DayView.expandAllDayRow}"),
                  "aria-expanded": false
                }
              }
            },
            allDayEvents: allDayEvents == null ? void 0 : allDayEvents.element,
            // May have been configured away
            $scrollerPad: DomHelper.scrollBarPadElement
            // $ prefix -> not a reference
          }
        } : null,
        dayHeaderElement: {
          className: "b-day-column-header",
          children: {
            dayHeaderScrollerElement: {
              className: "b-day-column-header-scroller",
              children: {
                dayHeaderContentElement: {
                  className: "b-day-column-content"
                }
              }
            },
            $scrollerPad: DomHelper.scrollBarPadElement
            // $ prefix -> not a reference
          }
        },
        dayContentElement: {
          role: "presentation",
          className: {
            "b-dayview-day-content": 1
          },
          children: {
            timeAxisElement: {
              children: me.getTimeElementConfigs(),
              className: {
                "b-timeaxis-container": 1
              },
              "aria-hidden": true
            },
            dayContainerElement: {
              role: "row",
              className: {
                "b-dayview-day-container": 1
              }
            }
          }
        },
        horizontalScrollerElement: {
          className: "b-virtual-scrollers b-hide-display",
          children: [{
            className: "b-virtual-scroller"
          }, DomHelper.scrollBarPadElement]
        }
      }
    });
  }
  async onDayContentMousewheel(e) {
    if (e.ctrlKey) {
      e.preventDefault();
      const { zoomOnMouseWheel } = this, multiplicationFactor = typeof zoomOnMouseWheel === "number" ? zoomOnMouseWheel : 1, pointerOffsetY = e.clientY - this.scrollable.element.getBoundingClientRect().y, reqDelta = -Math.min(Math.abs(e.deltaY), 10) * Math.sign(e.deltaY);
      await this.zoomPromise;
      this.zoomPromise = this.zoomBy(reqDelta * multiplicationFactor, pointerOffsetY, this.allowZoomUnderflow);
    }
  }
  zoomIn() {
    this.zoomBy(2);
  }
  zoomOut() {
    this.zoomBy(-2, void 0, true);
  }
  /**
   * Zooms the timeline by incrementing the {@link #property-hourHeight} by the requested pixel delta.
   * @param {Number} reqDelta The number of pixels by which to increment the {@link #property-hourHeight}
   * @param {Number|String} [zoomCenter] The center time to zoom in to. This may be a number of pixels
   * down the DayView viewport, or it may be a time to use as the center in the format `HH:MM:ss`.
   * __If omitted, the visual central time in the viewport is used__.
   *
   * Note that this will usually require a layout update which happens in the next animation frame
   * so to postprocess the new state of the view, the returned Promise must be awaited.
   * @async
   */
  zoomBy(reqDelta, zoomCenter = Math.min(this.scrollable.scrollHeight, this.scrollable.clientHeight) / 2 - 1, overrideClientHeightCheck) {
    return new Promise((resolve) => {
      const me = this, hoursInDay = me.getDayLength("h"), dayHeight = me.hourHeight * hoursInDay;
      me.updateElementLayout.cancel();
      zoomCenter = Math.min(zoomCenter, dayHeight);
      const {
        scrollable,
        hourHeight: oldHourHeight
      } = me, { clientHeight } = scrollable, { scrollTop } = scrollable.element, critical = reqDelta < 0 && dayHeight >= clientHeight - 5 && !overrideClientHeightCheck, zoomCenterTime = DateHelper.round(typeof zoomCenter === "number" ? me.getDateFromPosition(me.dayContainerElement.offsetWidth / 2, zoomCenter + scrollTop, true) : DateHelper.parse(zoomCenter, "HH:mm:ss"), me.increment), minHourHeight = critical ? me.dayContentClientHeight / hoursInDay : me.minHourHeight, pointerOffsetY = me.getPositionFromTime(zoomCenterTime) - scrollTop;
      const newHourHeight = Math.max(Math.max(oldHourHeight + reqDelta, minHourHeight), me.minHourHeight);
      if (critical && newHourHeight * hoursInDay < clientHeight) {
        me.fitHours = true;
        return resolve();
      }
      if (oldHourHeight !== newHourHeight) {
        scrollable.overflowY = me.configuredOverflowY;
        me.ion({
          layoutUpdate: () => {
            scrollable.scrollTo(null, me.getPositionFromTime(zoomCenterTime) - pointerOffsetY);
            scrollable.syncPartners(false, true);
            resolve();
          },
          once: true,
          prio: 1e3
        });
        me.fitHours = false;
        me.hourHeight = newHourHeight;
      } else {
        me.configuredOverflowY = scrollable.overflowY;
        if (newHourHeight === minHourHeight) {
          scrollable.overflowY = "hidden";
        }
        resolve();
      }
    });
  }
  /**
   * Zooms the timeline by setting the {@link #property-hourHeight} to the requested pixel value.
   * @param {Number} newHourHeight The new {@link #property-hourHeight} in pixels.
   * @param {Number|String} [zoomCenter] The center time to zoom in to. This may be a number of pixels
   * down the DayView viewport, or it may be a time to use as the center in the format `HH:MM:ss`.
   * __If omitted, the visual center of the viewport is used__.
   *
   * Note that this will usually require a layout update which happens in the next animation frame
   * so to postprocess the new state of the view, the returned Promise must be awaited.
   * @async
   */
  zoomTo(newHourHeight, zoomCenter) {
    return this.zoomBy(newHourHeight - this.hourHeight, zoomCenter, true);
  }
  /**
   * Zooms to fit all visible events within the vertical scroll viewport.
   * @param {Object} [options] How to scroll.
   * @param {Number} [options.edgeOffset] edgeOffset A margin around the target to bring into view.
   * @param {Object|Boolean|Number} [options.animate] Set to `true` to animate the scroll by 300ms,
   * or the number of milliseconds to animate over, or an animation config object.
   * @param {Number} [options.animate.duration] The number of milliseconds to animate over.
   * @param {String} [options.animate.easing] The name of an easing function.
   * @returns {Promise} A promise which is resolved when the target has been scrolled into view.
   * @async
   */
  async zoomToFit(options) {
    const me = this, events = me.eventStore.getEvents({
      startDate: me.startDate,
      endDate: me.endDate,
      filter: (e) => !e.isInterDay
    }), [start, end] = events.reduce((r, e) => {
      const startHour = DateHelper.getTimeOfDay(DateHelper.floor(e.startDate, "0.5h"), "h"), endHour = DateHelper.getTimeOfDay(DateHelper.ceil(e.endDate, "0.5h"), "h");
      return [startHour < r[0] ? startHour : r[0], endHour > r[1] ? endHour : r[1]];
    }, [24, 0]);
    await me.zoomTo(Math.floor(me.scrollable.clientHeight / (end - start)));
    await me.scrollTo(start, options);
  }
  createCellMap() {
    const { showAllDayHeader } = this;
    return super.createCellMap({
      dayTime: this.dayTime,
      // Normally, DayView only displays intraday events, so only those which start *on*
      // the cell's date and only those which fit completely *inside* the cell's date.
      // showDayHeader flips those assumptions because all intersecting events need to
      // be collected because we have nowhere else to show interday events.
      startOnly: showAllDayHeader,
      allowPartial: !showAllDayHeader,
      skipPropagate: true,
      rawEvents: true,
      filter: this.cellMapEventFilter
    });
  }
  cellMapEventFilter(eventRecord) {
    const { dayTime, hiddenNonWorkingDays, showAllDayHeader } = this;
    return (!showAllDayHeader || dayTime.isIntraDay(eventRecord) && !eventRecord.allDay) && // Event is eligible if it's within our view's day start/end range
    dayTime.intersects(eventRecord) && // AND it's not in a hidden nonworking day
    !hiddenNonWorkingDays[dayTime.dayOfWeek(eventRecord.startDate)];
  }
  getDayLength(as = "ms") {
    return this.dayTime.duration(as);
  }
  changeIncrement(increment) {
    if (typeof increment === "string") {
      increment = DateHelper.parseDuration(increment);
      return DateHelper.as("ms", increment.magnitude, increment.unit);
    }
    return increment;
  }
  get dayStartHour() {
    return this.dayStartTime == null ? 0 : Math.floor(this.dayStartTime / MILLIS_PER_HOUR2);
  }
  get dayEndHour() {
    return this.dayEndTime == null ? 24 : Math.ceil(this.dayEndTime / MILLIS_PER_HOUR2);
  }
  get dayStartMs() {
    return this.dayStartTime == null ? 0 : this.dayStartTime;
  }
  get dayEndMs() {
    return this.dayEndTime == null ? 24 * MILLIS_PER_HOUR2 : this.dayEndTime;
  }
  /**
   * This is the increment in milliseconds from the floored day start hour to the configured day start time.
   *
   * This is used to calculate the offset by which to move the timeAxis ticks when the start is not on an hour boundary.
   * @internal
   */
  get dayStartOffset() {
    return this.dayTime.startTimeOffsetMs / MILLIS_PER_HOUR2 * this.hourHeight;
  }
  changeEventFilter(filter2) {
    if (filter2 === void 0) {
      filter2 = (event) => !this.isAllDayEvent(event);
    }
    return filter2;
  }
  changeEventLayout(config, existing) {
    return DayLayout.reconfigure(existing, config, {
      owner: this,
      defaults: {
        owner: this
      }
    });
  }
  updateHideEmptyDays(hideEmptyDays) {
    if (!this.isConfiguring) {
      this._allDayEvents && (this._allDayEvents.hideEmptyDays = hideEmptyDays);
    }
  }
  updateDateFilter(dateFilter) {
    if (!this.isConfiguring) {
      this._allDayEvents && (this._allDayEvents.dateFilter = dateFilter);
    }
  }
  updateNonWorkingDays(nonWorkingDays) {
    var _a5, _b2;
    const me = this;
    (_a5 = super.updateNonWorkingDays) == null ? void 0 : _a5.call(this, nonWorkingDays);
    me._allDayEvents && (me._allDayEvents.nonWorkingDays = nonWorkingDays);
    if (!me.isConfiguring) {
      (_b2 = me._cellMap) == null ? void 0 : _b2.clear();
      me.refresh();
    }
  }
  updateMaxAllDayHeight(maxAllDayHeight) {
    if (this.alldayRowElement) {
      DomHelper.setLength(this.alldayRowElement, "max-height", maxAllDayHeight);
    }
  }
  get hideNonWorkingDays() {
    return this._hideNonWorkingDays === true && this.duration === 1 ? false : this._hideNonWorkingDays;
  }
  set hideNonWorkingDays(hideNonWorkingDays) {
    super.hideNonWorkingDays = hideNonWorkingDays;
  }
  updateHideNonWorkingDays(hideNonWorkingDays) {
    var _a5;
    const me = this, { _allDayEvents } = me;
    super.updateHideNonWorkingDays(hideNonWorkingDays);
    _allDayEvents && (_allDayEvents.hideNonWorkingDays = me.duration === 1 && !_allDayEvents.fullWeek ? false : me._hideNonWorkingDays);
    if (!me.isConfiguring) {
      (_a5 = me._cellMap) == null ? void 0 : _a5.clear();
      me.refresh();
    }
  }
  updateSyncCalendarDate(syncCalendarDate) {
    if (!this.isConfiguring) {
      this._allDayEvents && (this._allDayEvents.syncCalendarDate = syncCalendarDate);
    }
  }
  syncHours() {
    var _a5;
    const me = this;
    (_a5 = me._cellMap) == null ? void 0 : _a5.clear();
    DomSync.sync({
      targetElement: me.timeAxisElement,
      domConfig: {
        onlyChildren: true,
        children: me.getTimeElementConfigs()
      }
    });
    me.updateElementLayout.now();
    me.refresh();
  }
  syncCurrentTimeIndicators() {
    var _a5, _b2, _c2, _d;
    const me = this, now2 = ((_a5 = me.calendar) == null ? void 0 : _a5.dateTimeNow) || /* @__PURE__ */ new Date(), {
      showCurrentTime,
      dayTime,
      currentTimeIndicatorRenderer
    } = me, {
      style
    } = me.element, time = (showCurrentTime == null ? void 0 : showCurrentTime.showTime) ? DateHelper.round(now2, { unit: "minute", magnitude: 1 }) : now2;
    if (dayTime.contains(now2)) {
      style.setProperty("--current-time", `"${DateHelper.format(time, me.timeAxisTimeFormat)}"`);
      style.setProperty("--current-time-seconds", dayTime.delta(time, "second"));
    } else {
      style.setProperty("--current-time", "");
      style.setProperty("--current-time-seconds", "");
    }
    if (showCurrentTime && DateHelper.betweenLesser(now2, me.startDate, me.endDate) && dayTime.contains(now2)) {
      const {
        currentTimeElement,
        timeAxisElement
      } = me;
      if (showCurrentTime.showTime) {
        if (currentTimeElement.parentNode !== timeAxisElement) {
          timeAxisElement == null ? void 0 : timeAxisElement.insertBefore(currentTimeElement, timeAxisElement.firstChild);
        }
      } else {
        currentTimeElement.remove();
      }
      if (currentTimeIndicatorRenderer) {
        (_b2 = me.getDayElement(now2)) == null ? void 0 : _b2.appendChild(me.currentTimeIndicator);
      }
    } else {
      (_c2 = me._currentTimeIndicator) == null ? void 0 : _c2.remove();
      (_d = me._currentTimeElement) == null ? void 0 : _d.remove();
    }
  }
  updateShowCurrentTime(showCurrentTime) {
    const { dayContentElement } = this;
    dayContentElement.classList.toggle("b-custom-current-time", Boolean(this.currentTimeIndicatorRenderer));
    dayContentElement.classList.toggle("b-hide-current-time", !Boolean(showCurrentTime));
    dayContentElement.classList.toggle("b-show-current-time", Boolean(showCurrentTime == null ? void 0 : showCurrentTime.showTime));
    dayContentElement.classList.toggle("b-show-time-full-width", Boolean(showCurrentTime == null ? void 0 : showCurrentTime.fullWidth));
    dayContentElement.classList.toggle("b-show-time-on-top", Boolean(showCurrentTime == null ? void 0 : showCurrentTime.onTop));
    if (!this.isConfiguring) {
      this.syncCurrentTimeIndicators();
    }
  }
  get currentTimeIndicator() {
    const result = this._currentTimeIndicator || (this._currentTimeIndicator = DomHelper.createElement(this.callback(this.currentTimeIndicatorRenderer, this)));
    result.style.top = `var(--current-time-position)`;
    return result;
  }
  get currentTimeElement() {
    return this._currentTimeElement || (this._currentTimeElement = DomHelper.createElement({
      retainElement: true,
      className: "b-time-axis-current-time",
      children: [{
        className: "b-current-time-text"
      }]
    }));
  }
  /**
   * Iterates the hours in the {@link #config-dayStartTime configured day}, starting from the configured start hour,
   * calling the passed callback.
   * @param {Function|String} fn A function or the name of a function in the ownershop hierarchy to call.
   * @param {Number} fn.hour The hour being visited.
   * @param {Date} fn.date A Date object encapsulating the hour being visited.
   * @param {Number} fn.i Zero based loop counter.
   */
  eachHour(fn2) {
    const me = this, { dayTime } = me, { startHour } = dayTime, hours2 = dayTime.duration("h");
    fn2 = me.resolveCallback(fn2);
    for (let date2 = new Date(2e3, 5, 15, startHour, 0, 0), i = 0; i < hours2; i++) {
      fn2.handler.call(fn2.thisObj, date2.getHours(), new Date(date2), i);
      date2.setHours(date2.getHours() + 1);
    }
  }
  getTimeElementConfigs() {
    const me = this, { startHour } = me.dayTime, result = [];
    me.eachHour((hour2, date2, i) => {
      const ticks = me.createLeafTicks(date2);
      if (!i && me.showDayStartHour) {
        ticks.unshift({
          className: "b-dayview-timeaxis-tick b-dayview-hour-tick b-dayview-start-hour",
          text: DateHelper.format(new Date(2e3, 5, 15, startHour, 0, 0), me.timeAxisTimeFormat)
        });
      }
      date2.setHours(hour2 = date2.getHours() + 1);
      date2.setMinutes(0);
      result.push({
        className: `b-dayview-timeaxis-time b-dayview-timeaxis-time-${hour2 < 10 ? "0" : ""}${hour2}`,
        children: ticks.concat([{
          className: "b-dayview-timeaxis-tick b-dayview-hour-tick",
          text: DateHelper.format(date2, me.timeAxisTimeFormat)
        }])
      });
    });
    if (me.coreHours && me._simpleCoreHours) {
      result.unshift({
        className: "b-dayview-timeaxis-background",
        style: {
          backgroundImage: me.getCoreHoursGradient(me.coreHours)
        }
      });
    }
    return result;
  }
  createLeafTicks(date2) {
    return this[this.sixMinuteTicks ? "createSixMinuteTicks" : "createFiveMinuteTicks"](date2);
  }
  createFiveMinuteTicks(date2) {
    const result = [];
    for (let i = 1; i < 12; i++) {
      date2.setMinutes(i * 5);
      result.push({
        className: {
          "b-dayview-timeaxis-tick": 1,
          "b-dayview-tick-level-4": 1,
          // Five minutes
          "b-dayview-tick-level-3": !(i & 1),
          // Ten minutes
          "b-dayview-tick-level-2": !(i % 3),
          // Fifteen minutes
          "b-dayview-tick-level-1": i === 6
          // Half hour
        },
        text: DateHelper.format(date2, this.timeAxisTimeFormat)
      });
    }
    return result;
  }
  createSixMinuteTicks(date2) {
    const result = [];
    for (let i = 1; i < 10; i++) {
      date2.setMinutes(i * 6);
      result.push({
        className: {
          "b-dayview-timeaxis-tick": 1,
          "b-dayview-tick-level-3": 1,
          // Six minutes
          "b-dayview-tick-level-2": !(i & 1),
          // Twelve minutes
          "b-dayview-tick-level-1": i === 5
          // Half hour
        },
        text: DateHelper.format(date2, this.timeAxisTimeFormat)
      });
    }
    return result;
  }
  changeCoreHours(coreHours) {
    const me = this;
    me._simpleCoreHours = ObjectHelper.isObject(coreHours) || Array.isArray(coreHours) && coreHours.length !== 7;
    if (me._simpleCoreHours) {
      coreHours = ObjectHelper.clone(coreHours);
      coreHours._overlayDay = coreHours.overlayDay;
      Object.defineProperty(coreHours, "overlayDay", {
        set(v) {
          this._overlayDay = v;
          me.updateCoreHours();
        },
        get(v) {
          return this._overlayDay;
        }
      });
    }
    return coreHours;
  }
  updateCoreHours() {
    if (!this.isConfiguring) {
      this.syncHours();
    }
  }
  changeScrollable() {
    this.refreshDayBackground();
    const result = super.changeScrollable(...arguments);
    this.configuredOverflowY = result == null ? void 0 : result.overflowY;
    return result;
  }
  changeShowTime(showTime) {
    if (showTime === true) {
      return { startTime: true, endTime: false };
    }
    return showTime;
  }
  updateShowTime() {
    if (!this.isConfiguring) {
      this.refresh();
    }
  }
  changeStartDate() {
    return this.dayOfDate(super.changeStartDate(...arguments));
  }
  updateRange(range) {
    !this.isConfiguring && this.allDayEvents && (this.allDayEvents.range = range);
    super.updateRange(...arguments);
  }
  updateShiftIncrement(shiftIncrement) {
    var _a5;
    !this.isConfiguring && this.allDayEvents && (this.allDayEvents.shiftIncrement = shiftIncrement);
    (_a5 = super.updateShiftIncrement) == null ? void 0 : _a5.call(this, ...arguments);
  }
  updateStartDate(startDate) {
    const { _allDayEvents } = this;
    if (_allDayEvents) {
      const incomingEndDate = !this.range && this.peekConfig("endDate"), allDayConfig = {
        startDate
      };
      if (incomingEndDate) {
        allDayConfig.endDate = incomingEndDate;
      }
      _allDayEvents == null ? void 0 : _allDayEvents.setConfig(allDayConfig);
    }
    super.updateStartDate(...arguments);
  }
  changeEndDate() {
    return this.dayOfDate(super.changeEndDate(...arguments));
  }
  updateEndDate(endDate) {
    var _a5, _b2;
    (_a5 = super.updateEndDate) == null ? void 0 : _a5.call(this, ...arguments);
    if (!this.updatingRange) {
      (_b2 = this._allDayEvents) == null ? void 0 : _b2.setConfig({
        date: this.date,
        endDate
      });
    }
  }
  changeMinDayWidth(minDayWidth) {
    return typeof minDayWidth === "number" ? Math.max(minDayWidth, 50) : typeof minDayWidth === "string" ? minDayWidth : null;
  }
  updateMinDayWidth(minDayWidth) {
    this.element.style.setProperty("--min-day-width", minDayWidth ? DomHelper.setLength(minDayWidth) : null);
    this._allDayEvents && (this._allDayEvents.minDayWidth = minDayWidth);
    this.updateElementLayout();
  }
  updateMinHourHeight(minHourHeight) {
    this.hourHeight = Math.max(this.hourHeight, minHourHeight);
  }
  changeHourHeight(hourHeight) {
    if (hourHeight) {
      return Math.max(hourHeight, this.minHourHeight);
    }
  }
  updateHourHeight(hourHeight) {
    if (this.isConfiguring) {
      this.milestoneHeight = Math.max(Math.min(hourHeight / 2, this.shortEventHeight), 22);
    }
    this.milestoneDuration = 36e5 / (hourHeight / this.milestoneHeight);
    this.shortEventDuration = 36e5 / (hourHeight / this.shortEventHeight);
    if (!this.isConfiguring) {
      this.updateElementLayout();
      if (this.fitHours) {
        this.refresh();
      }
    }
  }
  updateShortEventHeight(shortEventHeight) {
    this.updateHourHeight(this.hourHeight);
  }
  updateMinEventHeight(minEventHeight) {
    this.contentElement.style.setProperty("--dayview-min-event-height", DomHelper.setLength(minEventHeight));
  }
  get hourHeight() {
    return !this.isConfiguring && this.fitHours ? Math.max(this.dayContentClientHeight / this.getDayLength("hour"), this.minHourHeight) : this._hourHeight;
  }
  changeFitHours(fitHours) {
    const me = this;
    if (fitHours) {
      me.configuredMinHourHeight = me.minHourHeight;
    } else if (me.configuredMinHourHeight) {
      me.minHourHeight = me.configuredMinHourHeight;
    }
    return fitHours;
  }
  get dayContentClientHeight() {
    return DomHelper.floorPx(Rectangle.client(this.dayContentElement).height);
  }
  updateFitHours(fitHours) {
    const me = this;
    if (fitHours) {
      if (fitHours.minHeight) {
        me.minHourHeight = Math.max(fitHours.minHeight, 9);
      } else {
        me.minHourHeight = 17;
      }
    }
    if (!me.isConfiguring) {
      fitHours && (me.hourHeight = Math.max(me.dayContentClientHeight / me.getDayLength("hour"), me.minHourHeight));
    }
    me.element.classList.toggle("b-fit-hours", Boolean(fitHours));
    me.monitorResize = DomHelper.scrollBarWidth || fitHours;
  }
  updateEventSpacing() {
    var _a5;
    if (!this.isConfiguring) {
      (_a5 = this._cellMap) == null ? void 0 : _a5.clear();
      this.refresh();
    }
  }
  onInternalPaint({ firstPaint }) {
    var _a5;
    const me = this, {
      dayContainerElement,
      horizontalScrollerElement
    } = me, {
      scrollBarWidth: scrollBarWidth2
    } = DomHelper;
    (_a5 = super.onInternalPaint) == null ? void 0 : _a5.call(this, ...arguments);
    if (firstPaint) {
      const { allDayEvents } = me;
      me.horizontalScroller = new Scroller({
        widget: me,
        element: dayContainerElement,
        overflowX: scrollBarWidth2 ? "hidden-scroll" : true,
        overflowY: "clip",
        propagateSync: true,
        internalListeners: {
          overflowChange: "updateElementLayout",
          thisObj: me
        }
      });
      me.dayHeaderScroller = new Scroller({
        _id: `${me.id}-dayheader-scroller`,
        widget: me,
        element: me.dayHeaderScrollerElement,
        overflowX: scrollBarWidth2 ? "hidden-scroll" : true,
        overflowY: "clip",
        propagateSync: true
      });
      me.horizontalScroller.addPartner(me.dayHeaderScroller);
      if (allDayEvents) {
        allDayEvents.element.classList.remove("b-dayview-initializing");
        me.horizontalScroller.addPartner(allDayEvents.headerScroller);
      }
      ResizeMonitor.addResizeListener(me.dayContainerElement, me.onDayContainerResize.bind(me));
      me.scrollbarScroller = new Scroller({
        _id: `${me.id}-scrollbar-scroller`,
        widget: me,
        element: horizontalScrollerElement.firstChild,
        scrollWidth: dayContainerElement.scrollWidth - scrollBarWidth2,
        overflowX: true,
        overflowY: false
      });
      me.horizontalScroller.addPartner(me.scrollbarScroller);
      me.updateElementLayout.now();
      me.setInterval(me.syncCurrentTimeIndicators.bind(me), 15 * 1e3, "syncCurrentTimeIndicators");
      if (allDayEvents) {
        EventHelper.on({
          click: {
            element: me.cornerElement,
            handler: "onCornerClick"
          },
          // The scrollbar padding must not bubble any mousemove events to dragdrop.
          // This is because a mouseover of the allDay row *may*, if there are no
          // all day events, cause expansion of the allDay row, which *may* cause
          // vertical overflow, which *may* on some platforms cause this padding element
          // to pop into visibility below the cursor which would then cause
          // a dragleave.
          mousemove: {
            element: me.allDayEvents.element.nextSibling,
            handler: stopEvent,
            capture: true
          },
          thisObj: me
        });
        me.scrollable.ion({
          overflowChange: "syncScrollbarPadding",
          thisObj: me
        });
      }
    }
  }
  afterInternalPaint({ firstPaint }) {
    var _a5;
    (_a5 = super.afterInternalPaint) == null ? void 0 : _a5.call(this, ...arguments);
    if (firstPaint) {
      this.refresh();
    }
  }
  scrollToVisibleStartTime(waitForLoad = true) {
    if (this.visibleStartTime != null) {
      const me = this, {
        scrollable,
        allDayEvents,
        eventStore
      } = me, startScrollPos = me.getPositionFromTime(me.visibleStartTime), doScroll = async () => {
        var _a5;
        (_a5 = scrollable.scrollTo) == null ? void 0 : _a5.call(scrollable, null, startScrollPos, {
          animate: false,
          block: "start"
        });
      };
      if (startScrollPos) {
        if (startScrollPos > scrollable.maxY && allDayEvents && me.showAllDayHeader !== false) {
          if (waitForLoad && !eventStore.storage.generation) {
            eventStore.ion({
              refresh: () => me.scrollToVisibleStartTime(false),
              once: true,
              expires: {
                delay: 2e3,
                alt: doScroll
              }
            });
          } else {
            me.scrollToStartTimer = me.allDayEvents.ion({
              heightChange: doScroll,
              once: true,
              expires: {
                delay: 500,
                alt: doScroll
              }
            });
          }
        } else {
          doScroll();
        }
      }
    }
  }
  relayAllDayEvents(event) {
    if (!blockedAllDayEvents[event.type]) {
      return this.trigger(event.eventName, event);
    }
  }
  async onCornerClick() {
    const { allDayEvents } = this;
    if (!allDayEvents.isAnimating) {
      await allDayEvents.toggleExpandCollapse();
      if (!this.isDestroyed) {
        this.allDayToggleIcon.setAttribute("aria-label", allDayEvents.expanded ? this.L("L{collapseAllDayRow}") : this.L("L{expandAllDayRow}"));
        this.allDayToggleIcon.setAttribute("aria-expanded", allDayEvents.expanded);
      }
    }
  }
  syncScrollbarPadding() {
    if (!this.configuredHiddenVerticalScroll && this.allDayEvents) {
      if (DomHelper.scrollBarWidth) {
        if (!this.allDayEvents.isAnimating) {
          const {
            allDayEvents,
            alldayRowElement,
            dayHeaderElement,
            scrollable
          } = this, fitHours = this.fitHours && scrollable.clientHeight / this.getDayLength("hour") >= this.minHourHeight, hasOverflow = !fitHours && scrollable.hasOverflow(), {
            headerElement,
            scrollable: verticalScroller
          } = allDayEvents, expanded = allDayEvents.expanded || allDayEvents.autoHeight, allDayHasOverflow = Boolean(expanded && verticalScroller.hasOverflow());
          dayHeaderElement.classList[hasOverflow || allDayHasOverflow ? "add" : "remove"]("b-show-yscroll-padding");
          if (hasOverflow && allDayHasOverflow) {
            headerElement.classList.add("b-show-yscroll-padding");
            alldayRowElement.classList.remove("b-show-yscroll-padding");
            scrollable.overflowY = verticalScroller.overflowY = "auto";
          } else if (allDayHasOverflow !== hasOverflow) {
            if (allDayHasOverflow) {
              headerElement.classList.add("b-show-yscroll-padding");
              verticalScroller.overflowY = "auto";
              scrollable.overflowY = "scroll";
            } else {
              headerElement.classList.remove("b-show-yscroll-padding");
              alldayRowElement.classList.add("b-show-yscroll-padding");
              verticalScroller.overflowY = "hidden";
              scrollable.overflowY = !fitHours && "auto";
            }
          } else {
            alldayRowElement.classList.remove("b-show-yscroll-padding");
            headerElement.classList.remove("b-show-yscroll-padding");
            scrollable.overflowY = !fitHours && "auto";
            verticalScroller.overflowY = "hidden";
          }
        }
      } else {
        this.scrollable.overflowY = this.allDayEvents.scrollable.overflowY = "auto";
      }
    }
  }
  onInternalResize(element, width, height, oldWidth, oldHeight) {
    var _a5;
    const { _allDayEvents: allDayEvents } = this;
    super.onInternalResize(element, width, height, oldWidth, oldHeight);
    if (oldHeight) {
      ((_a5 = allDayEvents == null ? void 0 : allDayEvents.cellMap) == null ? void 0 : _a5.size) && (allDayEvents == null ? void 0 : allDayEvents.performResizeRefresh(allDayEvents.eventsPerCell, allDayEvents.eventContainerTop));
      if (height !== oldHeight) {
        this.updateElementLayout();
        this.syncScrollbarPadding();
      }
    }
  }
  onThemeChange() {
    this.updateElementLayout();
  }
  // Only the day container *height* is of interest generally.
  // Unless we have had a minDayWidth set and we show scrollbars, in which case horizontal scrolling
  // may come and go and the horizontalScrollerElement which shows a scrollbar might need toggling.
  onDayContainerResize(el, { height: oldHeight } = {}, { height }) {
    if (oldHeight && (height !== oldHeight || DomHelper.scrollBarWidth && this.minDayWidth)) {
      this.updateElementLayout();
      if (this.fitHours) {
        this.updateFitHours(this.fitHours);
      }
    }
  }
  async updateElementLayout() {
    var _a5;
    if ((_a5 = this.owner) == null ? void 0 : _a5.isDestroyed) {
      return;
    }
    const me = this, {
      dayHeaderContentElement,
      dayContainerElement,
      horizontalScrollerElement,
      dayHeaderScroller,
      horizontalScroller,
      scrollbarScroller,
      allDayEvents
    } = me, dayElements = dayContainerElement.querySelectorAll("[data-date]"), { scrollBarWidth: scrollBarWidth2 } = DomHelper;
    let i, end;
    if (allDayEvents && me.refreshCount) {
      const cornerElementClassList = me.cornerElement.classList;
      if (allDayEvents.hasOverflow) {
        cornerElementClassList.add("b-has-cell-overflow");
        cornerElementClassList.toggle("b-expanded", Boolean(allDayEvents.expanded));
      } else {
        cornerElementClassList.remove("b-has-cell-overflow");
      }
    }
    me.refreshDayBackground.now();
    for (i = 0, end = dayElements.length - 1; i <= end; i++) {
      dayElements[i].classList.toggle("b-last-cell", i === end);
    }
    if (allDayEvents) {
      me.syncScrollbarPadding();
    }
    if (scrollBarWidth2) {
      horizontalScrollerElement.classList.toggle("b-show-yscroll-padding", me.scrollable.hasOverflow());
    }
    if (dayElements.length) {
      const r = Rectangle.from(dayElements[dayElements.length - 1], dayContainerElement), clientWidth = DomHelper.roundPx(Rectangle.client(dayContainerElement).width), scrollWidth = DomHelper.roundPx(me.rtl ? clientWidth - r.left : r.right - 1);
      if (scrollWidth > clientWidth) {
        horizontalScrollerElement.classList.remove("b-hide-display");
        dayHeaderScroller.scrollWidth = scrollWidth;
        dayHeaderContentElement.style.minWidth = `${scrollWidth}px`;
        scrollbarScroller.scrollWidth = scrollWidth;
        horizontalScroller.overflowX = "hidden-scroll";
      } else {
        horizontalScrollerElement.classList.add("b-hide-display");
        dayHeaderScroller.scrollWidth = null;
        dayHeaderContentElement.style.minWidth = "";
        scrollbarScroller.scrollWidth = null;
        horizontalScroller.overflowX = false;
      }
      if (me.peekConfig("visibleStartTime")) {
        me.scrollToVisibleStartTime();
      }
    }
    if (me.hasPainted) {
      me.trigger("layoutUpdate");
    }
  }
  refreshDayBackground() {
    var _a5;
    const me = this, {
      contentElement,
      hourHeight,
      dayStartOffset,
      _hourHeightLevel,
      hourHeightLevel
    } = me, dayContentCls = new DomClassList(contentElement.classList), dayLength = me.getDayLength("hour"), dayHeight = hourHeight * dayLength, secondHeight = hourHeight / (60 * 60), halfHourHeight = (hourHeight - 2) / 2, fifteenMinuteHeight = (hourHeight - 4) / 4, twelveMinuteHeight = (hourHeight - 5) / 5, tenMinuteHeight = (hourHeight - 6) / 6, sixMinuteHeight = (hourHeight - 10) / 10, fiveMinuteHeight = (hourHeight - 12) / 12, contentElementStyle = contentElement.style, timeAxisWidth = Math.ceil(DateHelper.format(widestDate, me.timeAxisTimeFormat).length / 2);
    dayContentCls.remove("b-dayview-hourheight-level-1", "b-dayview-hourheight-level-2", "b-dayview-hourheight-level-3", "b-dayview-hourheight-level-4");
    hourHeightLevel.level && dayContentCls.add(`b-dayview-hourheight-level-${hourHeightLevel.level}`);
    dayContentCls.toggle("b-six-minute-ticks", Boolean(me.sixMinuteTicks));
    DomHelper.syncClassList(contentElement, dayContentCls);
    me.element.style.setProperty("--time-axis-width", `${timeAxisWidth}em`);
    contentElementStyle.setProperty("--day-length", dayLength);
    contentElementStyle.setProperty("--day-height", `${dayHeight}px`);
    contentElementStyle.setProperty("--hour-height", `${hourHeight}px`);
    contentElementStyle.setProperty("--half-hour-height", `${halfHourHeight}px`);
    contentElementStyle.setProperty("--fifteen-minute-height", `${fifteenMinuteHeight}px`);
    contentElementStyle.setProperty("--twelve-minute-height", `${twelveMinuteHeight}px`);
    contentElementStyle.setProperty("--ten-minute-height", `${tenMinuteHeight}px`);
    contentElementStyle.setProperty("--six-minute-height", `${sixMinuteHeight}px`);
    contentElementStyle.setProperty("--five-minute-height", `${fiveMinuteHeight}px`);
    contentElementStyle.setProperty("--leaf-tick-height", `${me.sixMinuteTicks ? sixMinuteHeight : fiveMinuteHeight}px`);
    contentElementStyle.setProperty("--second-height", `${secondHeight}px`);
    contentElementStyle.setProperty("--day-start-offset", `${dayStartOffset}px`);
    contentElementStyle.setProperty("--short-event-height", `${DomHelper.setLength(me.shortEventHeight || "")}`);
    (_a5 = me.onRefreshDayBackground) == null ? void 0 : _a5.call(me);
    if (hourHeightLevel.level !== (_hourHeightLevel == null ? void 0 : _hourHeightLevel.level)) {
      if (me.tickRenderer) {
        me.doRefresh();
      }
      me.trigger("tickChange", {
        old: _hourHeightLevel || hourHeightLevelZero,
        new: hourHeightLevel
      });
    }
  }
  updateSixMinuteTicks(sixMinuteTicks) {
    if (!this.isConfiguring) {
      this.updateElementLayout();
    }
  }
  changeHourHeightBreakpoints(hourHeightBreakpoints) {
    if (!Array.isArray(hourHeightBreakpoints)) {
      return Object.values(hourHeightBreakpoints);
    }
    return hourHeightBreakpoints;
  }
  updateHourHeightBreakpoints() {
    if (!this.isConfiguring) {
      this.updateElementLayout();
    }
  }
  get hourHeightLevel() {
    const me = this, {
      hourHeight,
      hourHeightBreakpoints,
      sixMinuteTicks
    } = me, steps = sixMinuteTicks ? sixMinuteSteps : fiveMinuteSteps;
    for (let i = sixMinuteTicks ? 2 : 3; i >= 0; i--) {
      if (hourHeight >= hourHeightBreakpoints[i]) {
        return me._hourHeightLevel = {
          level: i + 1,
          step: steps[i]
        };
      }
    }
    return me._hourHeightLevel = hourHeightLevelZero;
  }
  updateLocalization() {
    if (!this.isConfiguring && this.allDayTextElement) {
      DomSync.sync({
        targetElement: this.allDayTextElement,
        domConfig: {
          text: this.L("L{EventEdit.All day}")
        }
      });
    }
    super.updateLocalization();
  }
};
__publicField(DayView, "$name", "DayView");
__publicField(DayView, "type", "dayview");
DayView.initClass();
DayView._$name = "DayView";

// lib/Calendar/widget/DayAgendaView.js
var { MILLIS_PER_HOUR: MILLIS_PER_HOUR3 } = DayTime;
var zeroRect2 = new Rectangle(0, 0, 0, 0);
var flowDirection = {
  column: "column",
  vertical: "column",
  row: "row",
  horizontal: "row"
};
var DayAgendaView = class extends DayView {
  constructor() {
    super(...arguments);
    // Topsy-turvey hour heights means durations cannot be accurately determined.
    // Drag *moving* is fine because a drop *position* can be acurately determined.
    __publicField(this, "allowDragCreate", false);
    __publicField(this, "allowDragResizee", false);
  }
  static get delayable() {
    return {
      updateElementLayout: {
        type: "raf",
        cancelOutstanding: true
      },
      refreshDayBackground: {
        type: "raf",
        cancelOutstanding: true
      },
      scrollToVisibleStartTime: {
        type: "raf",
        cancelOutstanding: true
      }
    };
  }
  // dayTime
  /**
   * Returns the pixel coordinate on the time axis for the passed time of day.
   * Because hour elements may be variable height in this view, this returns the bottom Y position for the passed hour.
   * @param {Date|Number|String} time The hour number, 'HH:MM' time or a `Date` instance.
   * @internal
   * @returns {Number}
   */
  getPositionFromTime(time) {
    var _a5;
    if (DayTime.parse(time) < DayTime.parse(this.dayStartTime) - this.dayStartShift) {
      return 0;
    }
    const hour2 = DayTime.parse(time) / (1e3 * 60 * 60), hourEl = (_a5 = this.eventContentElement.querySelector("[data-date]")) == null ? void 0 : _a5.querySelector(`[data-hour="${hour2}"]`);
    return hourEl ? hourEl.offsetTop + hourEl.offsetHeight : 0;
  }
  getDateFromDomEvent(domEvent, precise = false) {
    const me = this, el = DomHelper.getEventElement(domEvent), date2 = this.getDateFromElement(el);
    if (date2 && el !== domEvent && me.dayContainerElement.contains(el)) {
      let hourEl = el.closest("[data-hour]"), hour2 = parseInt((hourEl == null ? void 0 : hourEl.dataset.hour) || 0);
      if (!hourEl) {
        hourEl = [...el.querySelectorAll(`[data-hour]${me.hideEmptyHours ? ".b-has-events" : ""}`)].at(-1);
        hour2 = Math.min(parseInt((hourEl == null ? void 0 : hourEl.dataset.hour) || -1) + 1, me.dayEndHour - 1);
      }
      date2.setHours(hour2);
    }
    return date2;
  }
  getDateFromPosition(clientX, clientY, local = false, keyParser) {
    const me = this, {
      eventContentElement,
      allDayEvents
    } = me, rect = local ? zeroRect2 : Rectangle.fromScreen(eventContentElement).roundPx().translate(-eventContentElement.scrollLeft), width = eventContentElement.scrollWidth, localX = local ? clientX : clientX - rect.x, dx = me.rtl ? me.eventContentElement.offsetWidth - localX : localX, dy = clientY - rect.y, overCell = me.getDayElementFromX(clientX), height = overCell.offsetHeight, date2 = me.getDateFromElement(overCell, keyParser) || me.date;
    if (allDayEvents && (dx < 0 || width < dx || dy < 0 || height < dy)) {
      return allDayEvents.getDateFromPosition(clientX, clientY, keyParser);
    }
    DateHelper.clearTime(
      date2,
      /*clone = */
      false
    );
    const hourCells = [...overCell.querySelectorAll(".b-day-agenda-hour")];
    let hourCell, i;
    for (i = 0, hourCell = hourCells[i]; i < hourCells.length && dy >= hourCell.offsetTop && dy > hourCell.offsetTop + hourCell.offsetHeight; hourCell = hourCells[i], i++)
      ;
    date2.setHours(parseInt(hourCell.dataset.hour));
    const hourPosition = (dy - hourCell.offsetTop) / hourCell.offsetHeight;
    date2.setMilliseconds(date2.getMilliseconds() + hourPosition * MILLIS_PER_HOUR3);
    return DateHelper[me.timeSnapType](date2, me.increment);
  }
  getBaseDayDomConfig(date2) {
    var _a5;
    const me = this, {
      dateFilter,
      resource
    } = me, key = me.dayTime.dateKey(date2), cellData = me.cellMap.get(key) || me.cellMap.set(key, me.createCellData(date2)).get(key), { day: day2 } = cellData, skipDay = me.hiddenNonWorkingDays[day2] && me.duration !== 1 || me.hideEmptyDays && !me.getEventsForDay(key).length || dateFilter && !me.callback(dateFilter, me, [cellData]);
    me.getConfig("showCurrentTime");
    if (!skipDay) {
      const nonWorkingDays = me.nonWorkingDays || me.month.nonWorkingDays, children = [];
      me.eachHour((hour2, time) => {
        time.setHours(hour2);
        children.push({
          className: {
            "b-day-agenda-hour": 1,
            "b-has-events": 0
          },
          dataset: {
            hour: hour2
          },
          children: {
            time: {
              className: {
                "b-day-agenda-hour-time": 1
              },
              text: DateHelper.format(time, me.timeAxisTimeFormat)
            },
            events: {
              className: {
                "b-day-agenda-hour-events": 1
              },
              children: []
            }
          }
        });
      });
      return [cellData, {
        tabIndex: -1,
        role: "gridcell",
        "aria-label": `${DateHelper.format(date2, "dddd Do MMMM")}${resource ? ` ${resource.name}` : ""}, ${me.L("L{eventCount}", ((_a5 = cellData.events) == null ? void 0 : _a5.length) || 0)}.`,
        dataset: {
          date: key
        },
        className: {
          "b-dayview-day-detail": 1,
          [me.dayCellCls]: 1,
          [me.nonWorkingDayCls]: nonWorkingDays[day2] || false,
          [me.weekendCls]: DateHelper.weekends[day2],
          [me.todayCls]: !(date2 - me.today),
          "b-last-cell": !(date2 - me.lastVisibleDate),
          [`b-day-of-week-${day2}`]: 1
        },
        style: {},
        children,
        // Match existing data-event-id elements first and ensure DOM order matches
        // children order.
        syncOptions: {
          syncIdField: "eventId",
          releaseThreshold: 0,
          strict: true
        }
      }];
    }
  }
  getDayDomConfig(date2) {
    const me = this, base = me.getBaseDayDomConfig(date2);
    if (base) {
      const { dayCellRenderer } = me, [cellData, dayDomConfig] = base;
      me.renderEvents(cellData, dayDomConfig);
      return DomHelper.normalizeChildren(
        dayCellRenderer ? me.callback(dayCellRenderer, me, [dayDomConfig, cellData]) : dayDomConfig
      );
    }
  }
  /**
   * Pushes event bar DomConfig definitions into the `children` property of the passed
   * `dayDomConfig` object
   * @param {DayCell} cellData An object containing information about the day cell being created.
   * @param {DomConfig} dayDomConfig The DomConfig element definition for the day cell.
   * @internal
   */
  renderEvents(cellData, dayDomConfig) {
    for (const eventRecord of cellData.events) {
      const hourDomConfig = dayDomConfig.children[eventRecord.startDate.getHours() - this.dayTime.startHour];
      if (hourDomConfig) {
        const eventDomConfig = this.createEventDomConfig({ date: cellData.date, eventRecord });
        this.insertActionButtons(eventDomConfig.children[0].children[0].children, eventRecord, cellData);
        hourDomConfig.className["b-has-events"] = 1;
        hourDomConfig.children.events.children.push(eventDomConfig);
      }
    }
    this.trigger("renderEvents", cellData);
  }
  onInternalResize(element, width, height, oldWidth) {
    super.onInternalResize(...arguments);
    if (width !== oldWidth && this.syncHourHeights) {
      this.syncHeights();
    }
  }
  // This view can't do this
  changeShowCurrentTime() {
    return false;
  }
  onDayContentMousewheel() {
  }
  /**
   * Does nothig in this class
   * @private
   */
  zoomBy() {
  }
  /**
   * Does nothig in this class
   * @private
   */
  zoomTo() {
  }
  /**
   * Does nothig in this class
   * @private
   */
  async zoomToFit() {
  }
  /**
   * This is invalid for this view
   * @private
   */
  get dayStartOffset() {
    return 0;
  }
  changeEventLayout() {
  }
  syncCurrentTimeIndicators() {
  }
  updateEventSpacing(eventSpacing) {
    this.element.style.setProperty("--event-spacing", DomHelper.setLength(eventSpacing));
    this.refreshDayBackground();
  }
  updateMinEventWidth(minEventWidth) {
    this.element.style.setProperty("--min-event-width", DomHelper.setLength(minEventWidth));
    this.refreshDayBackground();
  }
  updateHourSpacing(hourSpacing) {
    this.element.style.setProperty("--hour-event-padding-top", DomHelper.setLength(hourSpacing));
    this.refreshDayBackground();
  }
  updateGutterWidth(gutterWidth) {
    this.element.style.setProperty("--gutter-width", DomHelper.setLength(gutterWidth));
    this.refreshDayBackground();
  }
  updateHideEmptyHours(hideEmptyHours) {
    this.element.classList.toggle("b-hide-empty-hours", Boolean(hideEmptyHours));
    this.refreshDayBackground();
  }
  updateHideTimeAxis(hideTimeAxis) {
    this.element.classList.toggle("b-hide-timeaxis", Boolean(hideTimeAxis));
    this.refreshDayBackground();
  }
  updateHideBorders(hideBorders) {
    this.element.classList.toggle("b-hide-borders", Boolean(hideBorders));
    this.refreshDayBackground();
  }
  updateBorderColor(borderColor) {
    this.element.style.setProperty("--hour-border-color", borderColor);
  }
  changeEventFlowDirection(eventFlowDirection) {
    return flowDirection[eventFlowDirection];
  }
  updateEventFlowDirection(eventFlowDirection) {
    this.element.style.setProperty("--event-flow-direction", eventFlowDirection);
    this.element.classList.toggle("b-event-flow-row", eventFlowDirection === "row");
    this.refreshDayBackground();
  }
  updateSyncHourHeights(syncHourHeights) {
    this.element.classList.toggle("b-synced-hour-heights", Boolean(syncHourHeights));
    this.refresh();
  }
  updateOverHour(overHourDate, lastOverHourDate) {
    var _a5, _b2;
    lastOverHourDate && ((_a5 = this.getHourElement(lastOverHourDate)) == null ? void 0 : _a5.classList.toggle("b-hover-hour", false));
    overHourDate && ((_b2 = this.getHourElement(overHourDate)) == null ? void 0 : _b2.classList.toggle("b-hover-hour", true));
  }
  getHourElement(date2) {
    var _a5;
    return (_a5 = this.getDayElement(date2)) == null ? void 0 : _a5.querySelector(`[data-hour="${date2.getHours()}"]`);
  }
  // Template method from DayView
  onDayCellConfigGenerated(dayCells) {
    if (this.syncHourHeights) {
      const { timeAxisElement } = this;
      this.eachHour((hour2, date2, i) => {
        const hasEvents = dayCells.some((dayCell) => dayCell.children[i].className["b-has-events"]), tick = timeAxisElement.querySelector(`[data-hour="${hour2}"]`);
        dayCells.forEach((dayCell) => dayCell.children[i].className["b-has-events"] = hasEvents);
        tick.classList.toggle("b-has-events", hasEvents);
      });
    }
  }
  onRefreshDayBackground() {
    var _a5;
    (_a5 = super.onRefreshDayBackground) == null ? void 0 : _a5.call(this);
    this.whenVisible("syncHeights");
  }
  syncHeights() {
    const me = this, {
      timeAxisElement,
      dayContainerElement,
      syncHourHeights
    } = me;
    me.contentElement.style.setProperty("--hour-height", me.hideEmptyHours && me.hideBorders ? "auto" : `${me.hourHeight}px`);
    dayContainerElement.querySelectorAll(".b-day-agenda-hour").forEach((h) => h.style.minHeight = "");
    me.eachHour((hour2) => {
      const hourCells = [...dayContainerElement.querySelectorAll(`.b-day-agenda-hour[data-hour="${hour2}"]`)], maxHeight = syncHourHeights ? `${Math.max(...hourCells.map((h) => h.offsetHeight))}px` : "", tick = timeAxisElement.querySelector(`[data-hour="${hour2}"]`);
      hourCells.forEach((h) => {
        h.style.minHeight = maxHeight;
      });
      tick.style.minHeight = maxHeight;
    });
  }
  getTimeElementConfigs() {
    const result = [];
    this.eachHour((hour2, time) => {
      result.push({
        className: {
          "b-day-agenda-hour": 1
        },
        dataset: {
          hour: hour2
        },
        children: [{
          className: "b-day-agenda-hour-time",
          text: DateHelper.format(time, this.timeAxisTimeFormat)
        }]
      });
    });
    return result;
  }
};
__publicField(DayAgendaView, "$name", "DayAgendaView");
__publicField(DayAgendaView, "type", "dayagenda");
__publicField(DayAgendaView, "configurable", {
  // region Hidden configs
  /**
   * @hideconfigs shortEventDuration, eventHeight, HourHeightBreakPoints, increment, eventLayout, increment
   * timeAxisTimeFormat, fitHours, zoomOnMouseWheel, sixMinuteTicks, timeSnapType, showCurrentTime, shortEventCls
   */
  // No such thing as a short event in this view.
  // Event blocks are intrinsically heighted by content.
  shortEventCls: "",
  // endRegion
  increment: "1 h",
  showCurrentTime: false,
  /**
   * The height __in pixels__ of an *empty* hour row.
   *
   * Hour rows grow to accommodate events when they contain events.
   * @prp {Number} hourHeight
   * @default
   */
  hourHeight: 55,
  showTime: {
    startTime: true,
    endTime: true
  },
  /**
   * How much vertical whitespace between the hour cells.
   * @prp {Number|String}
   */
  hourSpacing: "0.5em",
  /**
   * Set this to true to hide empty hour rows in this day agenda.
   *
   * Note that if {@link #config-syncHourHeights} is set, then empy hours will show where any column
   * has events for that hour because all row meights must match in this case.
   * @prp {Boolean}
   */
  hideEmptyHours: null,
  /**
   * Set this to true to hide the time cells in the day columns.
   * @prp {Boolean}
   */
  hideTimeAxis: null,
  /**
   * The color of the borders (if any) between hour rows and day rows.
   * @prp {String}
   */
  borderColor: null,
  /**
   * Set this to true to hide the borders between cells.
   * @prp {Boolean}
   */
  hideBorders: null,
  title: "L{dayAgenda}",
  timeSnapType: "floor",
  /**
   * How much space to leave empty at the right side of day columns to allow interaction such as
   * double click or drag.
   * @prp {Number|String}
   * @default
   */
  gutterWidth: 10,
  /**
   * Which direction to flow events in an hour row, vertically (the default), or horizontally.
   * @prp {'column'|'row'}
   * @default 'column'
   */
  eventFlowDirection: null,
  /**
   * How much vertical space to leave between event bars in a cell.
   * @prp {Number|String}
   * @default
   */
  eventSpacing: 5,
  /**
   * By default the hours across a multiple day view may have different heights because of different
   * event loads.
   *
   * Soecify this as `true` to sync hour heights so that each spans the event load for the hour.
   * @prp {Boolean}
   */
  syncHourHeights: null,
  /**
   * The minimum event width when {@link #config-eventFlowDirection} is `'row'`.
   * @prp {Number|String}
   * @default
   */
  minEventWidth: null,
  overHour: null
});
DayAgendaView.initClass();
DayAgendaView._$name = "DayAgendaView";

// lib/Calendar/widget/MonthView.js
var evRegexp = /^(\d+)ev$/;
var expandGestures2 = {
  shrinkwrap: 1,
  expand: 1
};
var MonthView = class extends CalendarPanel.mixin(CalendarMixin_default, DayCellCollecter_default, DayCellRenderer_default) {
  static get configurable() {
    return {
      eventSorter: EventSorter.interDaySorterFn,
      localizableProperties: ["title", "stepUnit"],
      title: "L{Month}",
      stepUnit: "L{monthUnit}",
      dragUnit: "day",
      localeClass: this,
      descriptionFormat: "MMMM YYYY",
      dayNumberCentered: null,
      /**
       * Configure as `false` to hide the week number shown at the top-left of each day cell
       * @prp {Boolean}
       * @default
       */
      showWeekNumber: true,
      /**
       * The height of event bars in this view. This can be a numeric value in pixels or a CSS unit measure such
       * as `'2em'`.
       * @prp {Number|String}
       * @default
       */
      eventHeight: 20,
      minHeight: 485,
      /**
       * By default, weeks rows all flex to share the available height equally.
       *
       * To make them shrinkwrap their events to show all events in every row, configure this as `true`
       * @prp {Boolean}
       * @default false
       */
      autoRowHeight: {
        $config: "lazy",
        value: false
      },
      /**
       * By default, rows which have been modified by the {@link Calendar.feature.WeekExpander}
       * feature, __not by the {@link #config-autoRowHeight} setting__, to shinkwrap large content
       * are reset to flexed height on month change.
       *
       * To have rows persist their shrinkwrapped status across month changes, confgure this as `true`.
       *
       * If {@link #config-autoRowHeight} is set, then the new month always has auto heighted rows.
       * @prp {Boolean}
       * @default false
       */
      persistShrinkWrappedRows: null,
      /**
       * The maximum number of events to show in a cell when the row is shrinkwrapped.
       * Use this to keep rows to a sane size when using {@link #config-autoRowHeight},
       * or the {@link Calendar.feature.WeekExpander} feature.
       * @config {Number}
       * @default
       */
      maxEventsPerCell: 100,
      /**
       * By default, week rows flex to share available Panel height equally.
       *
       * This may be configured as a number, in which case it means pixels, or a CSS length.
       *
       * The non-standard unit `ev` may also be specified to mean "events". For example
       * `'3ev'` means rows will always be three events bars (plus the day header)
       * tall.
       *
       * This is a useful config when using {@link #config-autoRowHeight}, or using
       * {@link #config-overflowClickAction} when rows may be switched to shrinkwrapping
       * their event content and may shrink in height.
       *
       * Setting this config causes the month grid to become scrollable in the `Y` axis.
       * @config {Number|String}
       */
      minRowHeight: null,
      /**
       * How the view responds to clicking on a `+n more` button in an overflowing day cell.
       *
       * The default value, `'popup'`, means that a small dialog box showing the full complement
       * of events for that cell is shown aligned to the cell.
       *
       * When set to `'expand'`, then clicking the `+n more` button causes the encapsulating
       * row to expand to accommodate all events in that row with no overflow.
       *
       * Navigating to a new month resets the row to its default, flexed height.
       * @config {'popup'|'expand'} overflowClickAction
       * @default
       */
      overflowClickAction: "popup",
      // So that when clicking the prev and next buttons, the UI will change
      // even if a cell for the new date is present.
      alwaysRefreshOnMonthChange: true
    };
  }
  onCalendarKeyDown(keyEvent) {
    const me = this, {
      dayTime,
      eventContentElement
    } = me, { target } = keyEvent, eventCellSelector = `.${this.dayCellCls}`, from = target.closest(eventCellSelector) || target, eventCellFocused = from.matches(eventCellSelector), cellKey = eventCellFocused ? "date" : "header-date", dateKey = from.dataset[eventCellFocused ? "date" : "headerDate"], date2 = dayTime.parseKey(dateKey);
    let incr, focusTarget;
    switch (keyEvent.key) {
      case "ArrowUp":
        keyEvent.preventDefault();
        incr = -7;
        break;
      case "ArrowDown":
        keyEvent.preventDefault();
        incr = 7;
        break;
      case "ArrowLeft":
        keyEvent.preventDefault();
        incr = -1;
        break;
      case "ArrowRight":
        keyEvent.preventDefault();
        incr = 1;
        break;
      default:
        return super.onCalendarKeyDown(keyEvent);
    }
    const newDate = DateHelper.add(date2, incr, "d"), sibling = incr > 0 ? me.nextSibling : me.previousSibling;
    while (me.hiddenNonWorkingDays[newDate.getDay()]) {
      newDate.setDate(newDate.getDate() + Math.sign(incr));
    }
    focusTarget = eventContentElement.querySelector(`${eventCellSelector}[data-${cellKey}="${dayTime.dateKey(newDate)}"]`);
    if (!focusTarget && sibling) {
      const targetDate = incr > 0 ? me.firstVisibleDate : me.lastVisibleDate;
      focusTarget = sibling.eventContentElement.querySelector(`${eventCellSelector}[data-${cellKey}="${dayTime.dateKey(targetDate)}"]`);
    }
    focusTarget == null ? void 0 : focusTarget.focus();
  }
  get eventContainerHeight() {
    const me = this, { classList } = me.weeksElement;
    if (me._eventContainerHeight == null && me.isVisible && !me.isConfiguring) {
      classList.add("b-measuring-container-height");
      me._eventContainerHeight = super.eventContainerHeight;
      classList.remove("b-measuring-container-height");
    }
    return me._eventContainerHeight;
  }
  onCalendarStoreChange() {
    super.onCalendarStoreChange(...arguments);
    this.syncShrinkwrappedRows();
  }
  onDateChange({ changes }) {
    if (changes.m) {
      const me = this, { autoRowHeight } = me;
      if (autoRowHeight) {
        me.weekElements.forEach(({ classList }) => classList.add("b-shrinkwrapped"));
      }
      if (me.persistShrinkWrappedRows || autoRowHeight) {
        me.syncShrinkwrappedRows();
      } else {
        me.shrinkwrappedRows.forEach((r) => me.flexWeekRow(r));
      }
    }
  }
  /**
   * Returns the resource associated with this month view when used inside a {@link Calendar.widget.ResourceView}
   * @readonly
   * @member {Scheduler.model.ResourceModel} resource
   */
  // Override from DayCellRenderer
  // Called automatically on the CellOverflow${overflowPopupTrigger} event because of callOnFunctions
  onCellOverflowGesture({ date: date2 }) {
    if (expandGestures2[this.overflowClickAction.toLowerCase()]) {
      this.shrinkwrapWeekRow(date2);
    } else {
      super.onCellOverflowGesture(...arguments);
    }
  }
  // addCellHeaderContent mutates the cellHeader DomConfig block.
  // And if we are to have a day name element, returns the DomConfig for it.
  // It's called from DayCellRenderer#getCellDomConfig
  addCellHeaderContent(cellHeader, cellData) {
    const dayName2 = {
      className: {
        "b-day-name": true
      },
      role: "button"
    };
    cellHeader.children = [
      cellData.visibleColumnIndex || this.showWeekColumn ? null : {
        tabIndex: 0,
        role: "link",
        "aria-label": this.L("L{Calendar.weekOfYear}", cellData.week),
        className: "b-week-num",
        text: cellData.week[1]
      },
      dayName2
    ];
    return dayName2;
  }
  get shrinkwrappedRows() {
    return this.weeksElement.querySelectorAll(".b-shrinkwrapped");
  }
  get shrinkwrapRowHeights() {
    const me = this, rowHeights = [], { cellMap } = me;
    me.month.eachWeek((week, [date2]) => {
      let eventCount = 0;
      for (let i = 0; i < 7; i++, date2.setDate(date2.getDate() + 1)) {
        const cellData = cellMap.get(DateHelper.makeKey(date2));
        if (cellData) {
          eventCount = Math.max(eventCount, cellData.renderedEvents.length);
        }
      }
      rowHeights.push(eventCount);
    });
    return rowHeights.map((maxEventCount) => me.eventHeightInPixels * maxEventCount + me.eventSpacing * (maxEventCount + 1) + Math.ceil(me._eventContainerTop));
  }
  /**
   * Returns the number of complete event bars which will fit inside the referenced cell.
   *
   * It's only in MonthView when some rows are shrinkwrapped round their event content (meaning
   * either expanded or contracted away from the 1/6 height default) that there may be a customized
   * eventsPerCell for a certain date.
   * @internal
   */
  getEventsPerCell(date2) {
    const me = this;
    if (me.hasShrinkwrappedRows) {
      const rowIndex = Math.floor(DateHelper.diff(me.startDate, date2, "d") / 7);
      if (me.weekElements[rowIndex].classList.contains("b-shrinkwrapped")) {
        return me.maxEventsPerCell;
      } else {
        const firstCell = me.weekElements[rowIndex].querySelector(me.visibleCellSelector), eventContainerHeight = firstCell.offsetHeight - me.eventContainerTop;
        return Math.floor((eventContainerHeight + me.eventSpacing) / (me.eventHeightInPixels + me.eventSpacing));
      }
    } else {
      return me.eventsPerCell;
    }
  }
  getMaxEventsForWeek(week) {
    const { row } = this.getWeekContext(week);
    return Math.max(...Array.from(row.querySelectorAll(this.visibleCellSelector)).map((c) => {
      const cellData = this.cellMap.get(c.dataset.date);
      return (cellData == null ? void 0 : cellData.renderedEvents.length) || 0;
    }));
  }
  getWeekContext(week) {
    var _a5, _b2;
    let me = this, weekStart, visibleWeekStart, rowIndex;
    if (typeof week === "number") {
      rowIndex = week;
      weekStart = DateHelper.parseKey((_a5 = me.weekElements[week].querySelector("[data-date]")) == null ? void 0 : _a5.dataset.date);
    } else if (week.nodeType === 1) {
      weekStart = DateHelper.parseKey((_b2 = week.closest(".b-calendar-row").querySelector("[data-date]")) == null ? void 0 : _b2.dataset.date);
      rowIndex = Math.floor(DateHelper.diff(me.startDate, weekStart, "d") / 7);
    } else {
      const incr = (week.getDay(week) - me.weekStartDay + 7) % 7;
      weekStart = DateHelper.add(DateHelper.clearTime(week), -incr, "d");
      rowIndex = Math.floor(DateHelper.diff(me.startDate, week, "d") / 7);
    }
    if (weekStart) {
      visibleWeekStart = new Date(weekStart);
      while (me.hiddenNonWorkingDays[visibleWeekStart.getDay()]) {
        visibleWeekStart.setDate(weekStart.getDate() + 1);
      }
    }
    return {
      rowIndex,
      weekStart,
      visibleWeekStart,
      row: me.weekElements[rowIndex]
    };
  }
  /**
   * Causes the week row referenced by the parameter (Either a Date, or the **zero based** row index)
   * to size itself to exactly wrap the maximum number of events for any day of that week.
   *
   * If there are a *lot* of events, the row may grow in height. If few, or none, the row will shrink
   * in height. The day name header along the top will always be visible by default.
   *
   * The row has the CSS class `'b-shrinkwrapped'` added when it is in the shrinkwrapped state
   * to allow querying, and custom styling.
   *
   * See {@link #function-flexWeekRow} for the converse operation.
   *
   * @param {Date|Number} week Either the date of a day within the week, or the **zero based** week row
   * to shrinkwrap.
   */
  shrinkwrapWeekRow(week, isLastCall = true) {
    const me = this, {
      weekStart,
      row
    } = me.getWeekContext(week), {
      maxEventsPerCell,
      eventContainerTop
    } = me, wasShrinkwrapped = row.classList.contains("b-shrinkwrapped"), maxEventsForWeek = me.getMaxEventsForWeek(week), maxEventCount = maxEventsPerCell ? Math.min(maxEventsPerCell, maxEventsForWeek) : maxEventsForWeek, shrinkwrapHeight = me.eventHeightInPixels * maxEventCount + me.eventSpacing * (maxEventCount + 1) + Math.ceil(eventContainerTop), expanded = maxEventsForWeek > me.eventsPerCell, t = row.querySelector(".b-week-toggle-tool");
    if (isLastCall) {
      me.scrollable = {
        overflowY: "auto"
      };
    }
    row.classList.add("b-shrinkwrapped");
    if (row.classList.contains("b-empty-row")) {
      row.style.flex = "";
    } else {
      t && (t.dataset.btip = me.L("L{WeekExpander.collapseTip}"));
      row.classList.remove("b-has-overflow");
      row.classList.toggle("b-expanded", expanded);
      row.style.flex = expanded ? `0 0 ${shrinkwrapHeight}px` : `1 0 ${shrinkwrapHeight}px`;
    }
    me.hasShrinkwrappedRows = true;
    if (isLastCall) {
      me.refresh();
    }
    if (!wasShrinkwrapped) {
      me.trigger("weekShrinkwrap", {
        weekStart,
        element: row
      });
    }
    if (isLastCall) {
      if (!me.isAnimating) {
        me.isAnimating = true;
      }
      return new Promise((resolve) => {
        EventHelper.onTransitionEnd({
          element: row,
          property: "flex-basis",
          handler: "onAllWeekElementsExpanded",
          thisObj: me,
          args: [resolve]
        });
      });
    }
  }
  onAllWeekElementsExpanded(element, property, resolve) {
    this.isAnimating = false;
    this.syncCalendarWeekDaysWithScrollable();
    resolve();
  }
  /**
   * Causes the week row referenced by the parameter (Either a Date, or the **zero-based** row index)
   * to become flexed in height to share the available height of the Calendar equally with other
   * flexed rows.
   *
   * See {@link #function-shrinkwrapWeekRow} for the converse operation.
   *
   * @param {Date|Number} date Either the date of a day within the week, or the **zero based** week row
   * to flex.
   */
  async flexWeekRow(date2, isLastCall = true, allRows = false) {
    const me = this, {
      weekStart,
      row
    } = me.getWeekContext(date2), t = row.querySelector(".b-week-toggle-tool");
    if (row.classList.contains("b-shrinkwrapped")) {
      row.style.flex = "";
      row.classList.add("b-flexing");
      t && (t.dataset.btip = this.L("L{WeekExpander.expandTip}"));
      me.trigger("weekFlex", {
        weekStart,
        element: row
      });
      me._autoRowHeight = false;
      if (isLastCall && !me.isAnimating) {
        await me.executeAndAwaitAnimations(me.element, () => me.isAnimating = true);
      }
      if (isLastCall) {
        me.onAllWeekElementsFlexed(row, allRows);
      } else {
        me.onWeekElementFlexed(row);
      }
    }
  }
  onWeekElementFlexed(weekElement) {
    weekElement.classList.remove("b-shrinkwrapped", "b-flexing", "b-expanded");
  }
  onAllWeekElementsFlexed(weekElement, allRows) {
    const me = this;
    me.scrollable.overflowY = me.scrollable.hasOverflow("y");
    if (allRows) {
      me.shrinkwrappedRows.forEach((r) => r.classList.remove("b-shrinkwrapped", "b-flexing", "b-expanded"));
    } else {
      weekElement.classList.remove("b-shrinkwrapped", "b-flexing", "b-expanded");
    }
    me.isAnimating = false;
    me.hasShrinkwrappedRows = me.shrinkwrappedRows.length;
    me.refresh();
    me.syncCalendarWeekDaysWithScrollable();
  }
  // The header must allow a scrollbar width if the platform displays scrollbars
  syncCalendarWeekDaysWithScrollable() {
    var _a5;
    this.weekdaysHeader.classList[((_a5 = this.scrollable) == null ? void 0 : _a5.hasScrollbar()) ? "add" : "remove"]("b-show-yscroll-padding");
  }
  updateHideOtherMonthCells() {
    super.updateHideOtherMonthCells(...arguments);
    this.refresh();
  }
  updateEventHeight(height, oldHeight) {
    var _a5;
    const me = this;
    super.updateEventHeight(height, oldHeight);
    if (!me.isConfiguring) {
      if ((_a5 = me.minRowHeight) == null ? void 0 : _a5.match(evRegexp)) {
        me.updateMinRowHeight(me._minRowHeight);
      }
      me.syncShrinkwrappedRows();
      const padding = DomHelper.getEdgeSize(me.element, "padding", "tb");
      me.minHeight = // Month is usually 6 weeks
      ((me.eventHeightInPixels + 1) * 2 + me.eventSpacing * 3 + Math.ceil(me.eventContainerTop)) * 6 + // Add header height with borders
      me.weekdaysHeader.offsetHeight + 7 + // And view padding
      padding.height;
    }
  }
  updateMinRowHeight(minRowHeight) {
    var _a5, _b2;
    const me = this, eventCount = parseInt((_b2 = (_a5 = minRowHeight == null ? void 0 : minRowHeight.match) == null ? void 0 : _a5.call(minRowHeight, evRegexp)) == null ? void 0 : _b2[1]);
    if (!isNaN(eventCount)) {
      if (me.isConfiguring) {
        return me.ion({
          paint: "updateMinRowHeight",
          args: [minRowHeight],
          once: true
        });
      }
      minRowHeight = me.eventHeightInPixels * eventCount + me.eventSpacing * (eventCount + 1) + Math.ceil(me.eventContainerTop);
    }
    super.updateMinRowHeight(minRowHeight);
    if (me.isAnimating) {
      me.ion({
        animationEnd: "performResizeRefresh",
        thisObj: me,
        args: [me._eventsPerCell, me._eventContainerTop],
        once: true
      });
    } else {
      me.performResizeRefresh(me._eventsPerCell, me._eventContainerTop);
    }
  }
  changeMaxEventsPerCell(maxEventsPerCell) {
    return maxEventsPerCell == null ? this.constructor.configurable.maxEventsPerCell : maxEventsPerCell;
  }
  updateMaxEventsPerCell() {
    if (!this.isConfiguring) {
      this.syncShrinkwrappedRows();
    }
  }
  async updateAutoRowHeight(autoRowHeight, wasAutoRowHeight) {
    var _a5, _b2;
    if (this.initializingAutoRowHeight && autoRowHeight === Boolean(wasAutoRowHeight)) {
      return;
    }
    const me = this, { visibleWeekCount } = me, weekExpander = (_b2 = me.features || ((_a5 = me.calendar) == null ? void 0 : _a5.features)) == null ? void 0 : _b2.weekExpander;
    let finalPromise;
    if (weekExpander && autoRowHeight) {
      weekExpander.disabled = weekExpander.disabledByAutoRowHeight = true;
    }
    if (autoRowHeight) {
      for (let i = 0; i < visibleWeekCount; i++) {
        finalPromise = me.shrinkwrapWeekRow(i, i === visibleWeekCount - 1);
      }
    } else {
      for (let i = 0; i < visibleWeekCount; i++) {
        finalPromise = me.flexWeekRow(i, i === visibleWeekCount - 1, true);
      }
    }
    await finalPromise;
    if (weekExpander && autoRowHeight && weekExpander.disabledByAutoRowHeight) {
      weekExpander.disabled = weekExpander.disabledByAutoRowHeight = false;
    }
    if (!autoRowHeight) {
      me._eventContainerHeight = me._eventsPerCell = null;
      me.refresh();
    }
  }
  // When data changes or eventHeight changes, any shrinkwrapped rows need to be
  // kept in the correct shape;
  syncShrinkwrappedRows() {
    if (this.isVisible) {
      const { shrinkwrappedRows } = this;
      for (let i = 0, length = Math.min(shrinkwrappedRows.length, this.visibleWeekCount); i < length; i++) {
        this.shrinkwrapWeekRow(shrinkwrappedRows[i], i === length - 1);
      }
    } else {
      this.whenVisible("refreshNow");
      this.whenVisible("syncShrinkwrappedRows");
    }
  }
  changeScrollable(scrollable, oldScrollable) {
    scrollable = super.changeScrollable(scrollable, oldScrollable);
    if (scrollable == null ? void 0 : scrollable.overflowX) {
      this.weekdaysScrollable || (this.weekdaysScrollable = new Scroller({
        widget: this,
        element: this.weekdaysHeader,
        overflowX: "hidden-scroll"
      }));
      scrollable.addPartner(this.weekdaysScrollable, "x");
    }
    return scrollable;
  }
  async scrollTo(target, options) {
    const me = this, { scrollable } = me, targetDate = typeof target === "string" ? DateHelper.parse(target) : new Date(target), targetIsDate = !isNaN(targetDate);
    await me.scrollPromise;
    if (me.isDestroyed) {
      return;
    }
    await super.scrollTo(targetIsDate ? targetDate : target, { x: false, y: false });
    if (targetIsDate && scrollable) {
      const { firstVisibleDate, lastVisibleDate } = me;
      lastVisibleDate.setHours(23, 59, 59);
      targetDate.setTime(DateHelper.clamp(targetDate, firstVisibleDate, lastVisibleDate));
      let incr = targetDate.getDay() === (me.weekStartDay + 6) % 7 ? -1 : 1;
      while (!DomHelper.isVisible(me.getDayElement(targetDate))) {
        if (targetDate < firstVisibleDate || targetDate > lastVisibleDate) {
          incr = -incr;
          if (incr < 0) {
            return;
          }
        }
        targetDate.setDate(targetDate.getDate() + incr);
      }
      target = me.getDayElement(targetDate);
      if (target) {
        scrollable.scrollIntoView(target, ObjectHelper.assign({}, me.defaultScrollOptions, typeof options == "boolean" ? { animate: options } : options));
      }
    }
  }
  collectEvents(options) {
    if (this.hideOtherMonthCells) {
      const { year, month: month2 } = this.month;
      options.startDate = new Date(year, month2, 1);
      options.endDate = new Date(year, month2 + 1, 1);
    }
    options.getDateIndex = (date2) => Number(date2) === Number(this.firstVisibleDate || this.startDate) ? "date" : "startDate";
    return super.collectEvents(options);
  }
  getDayElement(date2, strict) {
    if (typeof date2 !== "string") {
      date2 = DateHelper.makeKey(date2);
    }
    if (strict && parseInt(date2.substr(5, 2)) !== this.month.month + 1) {
      return;
    }
    return super.getDayElement(date2);
  }
  /**
   * Determines what is under the cursor of the specified event or what is described by the given element.
   * @param {Event|Element} domEvent The event or element
   * @returns {CalendarHit}
   */
  calendarHitTest(domEvent) {
    var _a5, _b2;
    const hit = super.calendarHitTest(domEvent), target = DomHelper.getEventElement(domEvent);
    if (hit) {
      if (hit.date.getMonth() !== this.month.month && (this.disableOtherMonthCells || this.hideOtherMonthCells)) {
        return;
      }
      const weekElement = target.closest(".b-calendar-week"), week = (_a5 = weekElement == null ? void 0 : weekElement.dataset.week) == null ? void 0 : _a5.split(",").map(Number);
      if (week) {
        hit.cell = hit.cell || target.closest(".b-calendar-cell");
        hit.dayNumber = Number((_b2 = hit.cell) == null ? void 0 : _b2.dataset.columnIndex);
        hit.week = week;
        hit.weekElement = weekElement;
        hit.weekNumber = week[1];
        hit.weekOffset = week[1] - Number(this.weeksElement.firstElementChild.dataset.week.split(",")[1]);
      }
    }
    return hit;
  }
  getDateFromPosition(clientX, clientY) {
    const me = this, weekEls = me.weeksElement.childNodes;
    for (let rect, i = 0; i < weekEls.length; ++i) {
      rect = weekEls[i].getBoundingClientRect();
      if (rect.top <= clientY && clientY < rect.bottom) {
        if (rect.left <= clientX && clientX < rect.right) {
          const dx = me.rtl ? rect.right - clientX : clientX - rect.x, column = Math.floor(dx * me.weekLength / rect.width);
          if (me.hideNonWorkingDays) {
            const cellDates = Array.from(weekEls[i].querySelectorAll(me.visibleCellSelector)).map((e) => me.getDateFromElement(e));
            return cellDates[column];
          } else {
            const date2 = me.getDateFromElement(weekEls[i].querySelector(me.visibleCellSelector));
            date2.setDate(date2.getDate() + column);
            return date2;
          }
        }
      }
    }
    return null;
  }
  /**
   * Determines the week container element of the specified event or the given element.
   * @param {Event|Element} domEvent The event or element
   * @returns {Element}
   * @internal
   */
  getWeekElementFor(domEvent) {
    const target = DomHelper.getEventElement(domEvent);
    return (target == null ? void 0 : target.closest(".b-calendar-week")) || null;
  }
  updateEventStore(eventStore, was) {
    var _a5;
    (_a5 = super.updateEventStore) == null ? void 0 : _a5.call(this, eventStore, was);
    CalendarPanel.prototype.doRefresh.call(this);
  }
  updateOverflowClickAction() {
    this.refresh();
  }
  updateSixWeeks() {
    this._eventsPerCell = this._eventContainerTop = this._eventContainerHeight = null;
    super.updateSixWeeks(...arguments);
  }
  onMonthDateChange({ changes }) {
    if (changes.r && !this.sixWeeks) {
      this._eventsPerCell = this._eventContainerTop = this._eventContainerHeight = null;
    }
    super.onMonthDateChange(...arguments);
  }
  doRefresh() {
    var _a5;
    const me = this, {
      weekElements
    } = me;
    if (!me.isConfiguring) {
      me.getConfig("autoRowHeight");
      (_a5 = me._cellMap) == null ? void 0 : _a5.clear();
    }
    const result = super.doRefresh();
    for (let i = 0, { length } = weekElements; i < length; i++) {
      const row = weekElements[i], maxEvents = Array.prototype.reduce.call(row.querySelectorAll(me.visibleCellSelector), (result2, cell) => {
        const cellData = me.cellMap.get(cell.dataset.date);
        return result2 + ((cellData == null ? void 0 : cellData.renderedEvents.length) || 0);
      }, 0);
      row.classList.toggle("b-empty-row", !maxEvents);
    }
    me.syncCalendarWeekDaysWithScrollable();
    return result;
  }
  showEvent(eventRecord) {
    this.setDate(eventRecord.startDate);
  }
  changeDayNumberCentered(dayNumberCentered) {
    return Boolean(dayNumberCentered);
  }
  updateShowWeekColumn(showWeekColumn) {
    if (!showWeekColumn && !this.element.querySelector(".b-week-num")) {
      this.doRefresh();
    }
    super.updateShowWeekColumn(showWeekColumn);
  }
  updateShowWeekNumber(showWeekNumber) {
    this.element.classList.toggle("b-show-week-number", showWeekNumber);
  }
  updateHideNonWorkingDays(hideNonWorkingDays) {
    var _a5;
    (_a5 = super.updateHideNonWorkingDays) == null ? void 0 : _a5.call(this, hideNonWorkingDays);
    if (!this.isConfiguring) {
      this.refresh();
    }
  }
  updateDayNumberCentered(dayNumberCentered) {
    const me = this;
    if (!me._dayNumberCentered && !("nonCenteredDayNumShowWeekColumn" in me)) {
      me.nonCenteredDayNumShowWeekColumn = me.showWeekColumn;
    }
    me._dayNumberCentered = dayNumberCentered;
    me.element.classList[dayNumberCentered ? "add" : "remove"]("day-number-center");
    me.showWeekColumn = dayNumberCentered ? true : me.nonCenteredDayNumShowWeekColumn;
  }
  get dayNameSelector() {
    return this.showWeekColumn ? ".b-cal-cell-header" : super.dayNameSelector;
  }
  set dayNameSelector(dayNameSelector) {
    this._dayNameSelector = dayNameSelector;
  }
  set startDate(date2) {
    this.date = date2;
  }
  get startDate() {
    return super.startDate;
  }
  next() {
    this.date = DateHelper.add(this.date || this.startDate, 1, "month");
  }
  previous() {
    this.date = DateHelper.add(this.date || this.startDate, -1, "month");
  }
};
__publicField(MonthView, "$name", "MonthView");
__publicField(MonthView, "type", "monthview");
__publicField(MonthView, "delayable", {
  syncCalendarWeekDaysWithScrollable: {
    type: "raf",
    cancelOutstanding: true
  },
  // Need to handle cleanup after the row collapse animation
  // in the next AF so that all scrolling has been recalculated
  // and the overflowY can be set accurately
  onAllWeekElementsFlexed: {
    type: "raf",
    cancelOutstanding: true
  }
});
MonthView.initClass();
MonthView._$name = "MonthView";

// lib/Calendar/widget/WeekView.js
var WeekView = class extends DayView {
  static get configurable() {
    return {
      localizableProperties: ["title"],
      title: "L{Week}",
      range: {
        magnitude: 1,
        unit: "week"
      },
      responsive: {
        small: {
          descriptionFormat: "Wp YYYY"
        },
        large: {
          descriptionFormat: ["MMMM YYYY (Wp)", "S{MMM} - E{MMM YYYY} (S{Wp})"]
        },
        "*": {
          descriptionFormat: ["MMMM YYYY", "S{MMM} - E{MMM YYYY}"]
        }
      },
      week: null,
      // Override from DayView. This means that in addition to endDate being
      // locked to startDate, and being changed when startDate changes, startDate
      // is also locked to endDate and is changed when the endDate changes.
      fixedDuration: true
    };
  }
  /**
   * Returns the resource associated with this week view when used inside a {@link Calendar.widget.ResourceView}
   * @readonly
   * @member {Scheduler.model.ResourceModel} resource
   */
  calculateDuration() {
    return 7;
  }
  updateWeekStartDay(weekStartDay, oldWeekStartDay) {
    var _a5;
    (_a5 = super.updateWeekStartDay) == null ? void 0 : _a5.call(this, weekStartDay);
    if (!this.isConfiguring) {
      const startDate = new Date(this.startDate);
      startDate.setDate(startDate.getDate() + weekStartDay - oldWeekStartDay);
      this.startDate = startDate;
    }
  }
  changeMonth() {
    const result = super.changeMonth(...arguments);
    result.ion({
      weekChange: "onMonthWeekChange",
      thisObj: this
    });
    return result;
  }
  onMonthWeekChange({ source, newDate }) {
    var _a5;
    if (((_a5 = this.range) == null ? void 0 : _a5.unit) === "week") {
      this.week = source.getWeekNumber(newDate);
    }
  }
  changeWeek(week, oldWeek) {
    var _a5, _b2;
    const me = this;
    week = Array.isArray(week) ? week : [me.month.year, week];
    if (!ObjectHelper.isEqual(week, oldWeek)) {
      const minDate = me.minDate || ((_a5 = me.calendar) == null ? void 0 : _a5.minDate), maxDate = me.maxDate || ((_b2 = me.calendar) == null ? void 0 : _b2.maxDate);
      if (!isNaN(minDate) || !isNaN(maxDate)) {
        const { month: month2 } = me, weekStart = month2.getWeekStart(week), weekEnd = DateHelper.add(weekStart, me.duration, "d");
        if (!isNaN(minDate) && weekEnd <= minDate) {
          return month2.getWeekNumber(minDate);
        }
        if (!isNaN(maxDate) && weekStart > maxDate) {
          return month2.getWeekNumber(maxDate);
        }
      }
      return week;
    }
  }
  updateWeek(week) {
    const me = this, { month: month2, date: date2, dayTime } = me;
    if (!date2 || !ObjectHelper.isEqual(month2.getWeekNumber(date2), week)) {
      me.date = dayTime.shiftDate(month2.getWeekStart(week));
    }
    me.week = month2.getWeekNumber(me.date);
  }
};
__publicField(WeekView, "$name", "WeekView");
__publicField(WeekView, "type", "weekview");
WeekView.initClass();
WeekView._$name = "WeekView";

// lib/Calendar/widget/YearView.js
var isHoverGesture = {
  hover: 1,
  mouseover: 1
};
var emptyString5 = new String();
var countEvents = (v, e) => v + e.renderedEvents.filter((e13) => !e13.isOverflow).length;
var YearView = class extends Panel.mixin(CalendarMixin_default, DayCellCollecter_default, DayCellRenderer_default) {
  static get configurable() {
    return {
      textContent: false,
      localizableProperties: ["title", "stepUnit"],
      title: "L{Year}",
      scrollable: {
        overflowY: true
      },
      stepUnit: "L{yearUnit}",
      dragUnit: "day",
      month: true,
      /**
       * A date which specifies the year to display. All types of calendar view have a `date`
       * config which they use to set their visible date range according to their configuration
       * and type.
       * @config {Date}
       */
      date: {
        $config: {
          equal: "date"
        },
        value: null
      },
      descriptionFormat: "YYYY",
      /**
       * The year to display
       * @config {Number}
       */
      year: null,
      monitorResize: false,
      showWeekColumn: true,
      hideWeekColumnCls: "b-hide-week-numbers",
      /**
       * The class name to add to calendar cells which are in the previous or next month.
       * @config {String}
       * @private
       */
      otherMonthCls: "b-other-month",
      /**
       * By default, all months show six week rows. Pass `false` to only render as many rows
       * as needed.
       * @config {Boolean}
       */
      sixWeeks: true,
      /**
       * An empty function by default, but provided so that you can override it.
       *
       * This function is called for each cell rendered to allow developers to mutate the cell metadata, or
       * the CSS classes to be applied to the cell.
       *
       * The cellConfig (a {@link Core.helper.DomHelper#typedef-DomConfig}) definition passed as part of the
       * single parameter may be mutated to add `style` and `className`.
       *
       * ```javascript
       * dayCellRenderer : function(cellData) {
       *     // I don't like Mondays!
       *     if (cellData.date === 1) {
       *         cellData.cls['dayoff'] = true;
       *     }
       * }
       *
       * ```
       *
       * A non-null return value from the renderer is used as the content of the day cell element.
       *
       * @config {Function} dayCellRenderer
       * @param {YearCell} cellData A `DayCell` object that contains data about the cell.
       * @returns {DomConfig|String|null}
       *
       * @category Rendering
       */
      dayCellRenderer: null,
      monthSelector: ".b-yearview-month-name",
      focusable: false,
      /**
       * How to indicate the presence of events for a date. The default is `heatmap` which adds
       * classes depending on how many events intersect the date.Values may be:
       *
       * * `false` - Do not show events in cells.
       * * `true` - Show a heatmap of colours the intensity of which indicates event load.
       * * `'heatmap'` - Show a heatmap of colours the intensity of which indicates event load.
       * * `'count'` - Show a themeable badge containing the event count for a date.
       * * `'dots'` - Show small event-coloured bullets up to a maximum of `{@link #config-eventDots}.max`
       *   to indicate event presence.
       * to indicate event presence.
       * @prp {Boolean|'heatmap'|'dots'}
       * @default
       */
      showEvents: "heatmap",
      /**
       * When {@link #config-showEvents} is `'dots'`, this is the maximum number of dots to show
       * below the date number in the cell.
       *
       * The default is four. Note that increasing this may lead to a cluttered UI in which the dots
       * obscure the date figure in the cell.
       * @deprecated 6.1.2 Use {@link #property-eventDots} instead.
       *
       * @prp {Number}
       * @default 4
       */
      maxDots: null,
      /**
       * When {@link #config-showEvents} is `'dots'`, this controls the layout of the dots
       * below the day number.
       *
       * If the `max` property is 1, the dots will be coloured using the default event colour,
       * *not* the colour of the event. This is to create a consistent appearance.
       *
       * @prp {Object}
       * @property {Number} marginTop The margin above the dots. Default is 4.
       * @property {Number} max The maximum number of dots to show. Default is 4.
       * @property {Number} gap The gap between dots. Default is 3.
       * @property {Number} size The size of the dots. Default is 5.
       * @property {Boolean} stripe If `true`, the dots will be striped, meaning they will be
       * joined together with _no gap_ between them and rounded at each end. Default is `false`.
       */
      eventDots: {
        marginTop: 4,
        max: 4,
        gap: 3,
        size: 5
      },
      /**
       * When {@link #config-overflowPopupTrigger} is not a mouseover gesture, setting this to
       * a truthy value means that a tooltip containing the event count will be shown on hover of
       * a day cell.
       *
       * It may be specified as an Object which contains {@link Core.widget.Tooltip} config options,
       * and also the following option:
       *
       * - showNoEvents - When set to `true`, a tooltip containing "No events" is shown when
       * hovering empty day cells.
       *
       * For example:
       *
       * ```javascript
       * {
       *     modes : {
       *         year : {
       *             // We want it to show if there are no visible events for that date.
       *             // And we want the tip showing to the right of the cell
       *             eventCountTip : {
       *                 showNoEvents : true,
       *                 align        : 'l-r
       *             }
       *         }
       *     }
       * }
       * ```
       * @prp {Boolean|Object<String,Boolean|String>}
       * @default
       */
      eventCountTip: null
    };
  }
  /**
   * Returns the resource associated with this year view when used inside a {@link Calendar.widget.ResourceView}
   * @readonly
   * @member {Scheduler.model.ResourceModel} resource
   */
  onCalendarKeyDown(keyEvent) {
    var _a5, _b2, _c2, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p, _q;
    const {
      dayTime,
      eventContentElement
    } = this, { target } = keyEvent, eventCellSelector = `.${this.dayCellCls}:not(.b-other-month)`, eventCellFocusable = `${eventCellSelector}>[tabIndex]`, gridCellFocusable = `:is(.b-week-number-cell>[tabIndex],${eventCellFocusable})`, from = target.closest(eventCellFocusable) || target;
    if (keyEvent.key === "Escape") {
      keyEvent.preventDefault();
      (_b2 = (_a5 = from.closest(".b-yearview-month")) == null ? void 0 : _a5.querySelector(".b-yearview-month-name")) == null ? void 0 : _b2.focus();
      return;
    }
    if (from.matches(eventCellFocusable)) {
      const date2 = dayTime.parseKey(from.parentNode.dataset.date);
      switch (keyEvent.key) {
        case "ArrowUp":
          keyEvent.preventDefault();
          (_c2 = eventContentElement.querySelector(`${eventCellSelector}[data-date="${dayTime.dateKey(DateHelper.add(date2, -7, "d"))}"]>[tabIndex]`)) == null ? void 0 : _c2.focus();
          break;
        case "ArrowDown":
          keyEvent.preventDefault();
          (_d = eventContentElement.querySelector(`${eventCellSelector}[data-date="${dayTime.dateKey(DateHelper.add(date2, 7, "d"))}"]>[tabIndex]`)) == null ? void 0 : _d.focus();
          break;
        case "ArrowLeft":
          keyEvent.preventDefault();
          (_e = ElementVisitor.getVisitor(eventContentElement, gridCellFocusable).previous(from)) == null ? void 0 : _e.focus();
          break;
        case "ArrowRight":
          keyEvent.preventDefault();
          (_f = ElementVisitor.getVisitor(eventContentElement, gridCellFocusable).next(from)) == null ? void 0 : _f.focus();
          break;
        case "PageUp":
          keyEvent.preventDefault();
          date2.setMonth(date2.getMonth() - 1);
          date2.setDate(Math.min(date2.getDate(), DateHelper.getLastDateOfMonth(date2)));
          (_g = eventContentElement.querySelector(`${eventCellSelector}[data-date="${dayTime.dateKey(date2)}"]>[tabIndex]`)) == null ? void 0 : _g.focus();
          break;
        case "PageDown":
          keyEvent.preventDefault();
          date2.setMonth(date2.getMonth() + 1);
          date2.setDate(Math.min(date2.getDate(), DateHelper.getLastDateOfMonth(date2)));
          (_h = eventContentElement.querySelector(`${eventCellSelector}[data-date="${dayTime.dateKey(date2)}"]>[tabIndex]`)) == null ? void 0 : _h.focus();
          break;
        default:
          return super.onCalendarKeyDown(keyEvent);
      }
    } else if (from.matches(".b-week-number-cell > [tabIndex]")) {
      const date2 = dayTime.parseKey(from.parentNode.nextSibling.dataset.date);
      let week, sel;
      switch (keyEvent.key) {
        case "ArrowUp":
          keyEvent.preventDefault();
          week = DateHelper.getWeekNumber(DateHelper.add(date2, -1, "w"));
          sel = `[data-week="${week[0]},${week[1]}"]:not(.b-other-month)>.b-week-number-cell>[tabIndex]`;
          (_i = from.closest(".b-yearview-month").querySelector(sel) || eventContentElement.querySelector(sel)) == null ? void 0 : _i.focus();
          break;
        case "ArrowDown":
          keyEvent.preventDefault();
          week = DateHelper.getWeekNumber(DateHelper.add(date2, 1, "w"));
          (_j = eventContentElement.querySelector(`[data-week="${week[0]},${week[1]}"]:not(.b-other-month)>.b-week-number-cell>[tabIndex]`)) == null ? void 0 : _j.focus();
          break;
        case "ArrowLeft":
          keyEvent.preventDefault();
          (_k = ElementVisitor.getVisitor(eventContentElement, gridCellFocusable).previous(from)) == null ? void 0 : _k.focus();
          break;
        case "ArrowRight":
          keyEvent.preventDefault();
          (_l = ElementVisitor.getVisitor(eventContentElement, gridCellFocusable).next(from)) == null ? void 0 : _l.focus();
          break;
        case "PageUp":
          keyEvent.preventDefault();
          week = DateHelper.getWeekNumber(DateHelper.add(date2, -6, "w"));
          (_m = eventContentElement.querySelector(`[data-week="${week[0]},${week[1]}"]>.b-week-number-cell>[tabIndex]`)) == null ? void 0 : _m.focus();
          break;
        case "PageDown":
          keyEvent.preventDefault();
          week = DateHelper.getWeekNumber(DateHelper.add(date2, 6, "w"));
          (_n = eventContentElement.querySelector(`[data-week="${week[0]},${week[1]}"]>.b-week-number-cell>[tabIndex]`)) == null ? void 0 : _n.focus();
          break;
        default:
          return super.onCalendarKeyDown(keyEvent);
      }
    } else if (from.matches(".b-yearview-month-name")) {
      const date2 = dayTime.parseKey(from.dataset.monthDate);
      switch (keyEvent.key) {
        case "ArrowDown":
          keyEvent.preventDefault();
          (_o = from.parentElement.querySelector(gridCellFocusable)) == null ? void 0 : _o.focus();
          break;
        case "ArrowLeft":
          keyEvent.preventDefault();
          (_p = eventContentElement.querySelector(`[data-month-date="${dayTime.dateKey(DateHelper.add(date2, -1, "M"))}"]`)) == null ? void 0 : _p.focus();
          break;
        case "ArrowRight":
          keyEvent.preventDefault();
          (_q = eventContentElement.querySelector(`[data-month-date="${dayTime.dateKey(DateHelper.add(date2, 1, "M"))}"]`)) == null ? void 0 : _q.focus();
          break;
        default:
          return super.onCalendarKeyDown(keyEvent);
      }
    } else {
      return super.onCalendarKeyDown(keyEvent);
    }
  }
  // Override from DayCellCollecter
  // We don't propagate. We copy all events into renderedEvents.
  createCellMap(options) {
    const result = super.createCellMap(options = {
      skipPropagate: true,
      ...options
    });
    for (const cellData of result.values()) {
      cellData.renderedEvents.setEvents(cellData.events);
    }
    return result;
  }
  get date() {
    return this._date || !this.isConfiguring && this.firstEventDate || new Date(this.year, 0, 1);
  }
  next() {
    this.date = new Date(this.year + 1, this.date.getMonth(), this.date.getDate());
  }
  previous() {
    this.date = new Date(this.year - 1, this.date.getMonth(), this.date.getDate());
  }
  get firstEventDate() {
    const entries = [...this.cellMap.values()];
    if (entries.length) {
      return entries[0].date;
    }
  }
  get lastEventDate() {
    const entries = [...this.cellMap.values()];
    if (entries.length) {
      return entries[entries.length - 1].date;
    }
  }
  onCalendarPointerInteraction(domEvent) {
    var _a5, _b2;
    const me = this, superCall = super.onCalendarPointerInteraction, {
      target,
      type
    } = domEvent;
    if (type === "mousedown" || type.endsWith("click")) {
      me.clearTimeout(me.cellOverflowTimer);
    }
    if (type === "mousedown" && ((_a5 = me._overflowPopup) == null ? void 0 : _a5.isVisible) && target.closest(".b-cal-cell-overflow")) {
      domEvent.preventDefault();
    }
    if (me.overflowPopupTrigger === "click" && ((_b2 = me.autoCreate) == null ? void 0 : _b2.gesture) === "dblclick" && type === "click" && target.closest(`.${me.emptyCellCls}`) && !me.emptyCellRenderer) {
      me.cellOverflowTimer = me.setTimeout({
        fn: superCall,
        delay: 300,
        args: [domEvent]
      });
    } else {
      return superCall.apply(me, arguments);
    }
  }
  changeMonth(month2, oldMonth) {
    const me = this, listeners = {
      name: "yearChangeListener",
      yearChange: "onMonthYearChange",
      thisObj: me
    };
    me.detachListeners("yearChangeListener");
    if (month2 instanceof Month) {
      month2.ion(listeners);
      me._year = month2.year;
    } else {
      month2 = new Month({
        date: new Date(me.year || (/* @__PURE__ */ new Date()).getFullYear(), 0, 1),
        weekStartDay: me.weekStartDay,
        hideNonWorkingDays: me.hideNonWorkingDays,
        nonWorkingDays: me.nonWorkingDays,
        sixWeeks: me.sixWeeks,
        internalListeners: listeners
      });
    }
    if (me.nonWorkingDays == null) {
      me.nonWorkingDays = me._month.nonWorkingDays;
    }
    me._year = month2.year;
    return month2;
  }
  onMonthYearChange({ source: { year, date: date2 } }) {
    var _a5;
    const me = this;
    me._year = year;
    if (!me.isConfiguring) {
      (_a5 = me._cellMap) == null ? void 0 : _a5.clear();
      me.date = date2;
      me.refresh();
    }
  }
  // Override for the YearView. We have to scroll *months* into view
  scrollTo(date2, options = { animate: true }) {
    const me = this;
    date2 = me.ingestDate(date2.isEventModel ? date2.startDate : date2);
    if (!DateHelper.betweenLesser(date2, me.startDate, me.endDate) || !me.getDayElement(date2, true)) {
      me.date = date2;
    } else {
      const monthEl = me.eventContentElement.querySelector(`[data-month-index="${date2.getMonth()}"]`);
      monthEl && me.scrollable.scrollIntoView(monthEl, options);
    }
  }
  get eventsPerCell() {
    return 0;
  }
  get startDate() {
    return this.month.getOtherMonth(new Date(this.year, 0, 1)).startDate;
  }
  get endDate() {
    return DateHelper.add(this.month.getOtherMonth(new Date(this.year, 11, 1)).endDate, 1, "day");
  }
  /**
   * For a consistent API, allow startDate to set the year
   * @internal
   */
  set startDate(startDate) {
    this.date = startDate;
  }
  updateDate(date2) {
    this.month.date = date2;
    if (this.isVisible && !this.isConfiguring) {
      this.scrollTo(date2);
    }
  }
  changeYear(year) {
    if (this.isConfiguring) {
      return year;
    }
    this.month.year = year;
    return this.month.year;
  }
  changeShowEvents(showEvents, oldShowEvents) {
    return showEvents === true ? "heatmap" : showEvents;
  }
  updateShowEvents(showEvents, oldShowEvents) {
    const me = this, { classList } = me.contentElement;
    showEvents && classList.add(`b-show-events-${showEvents}`);
    classList.remove(`b-show-events-${oldShowEvents}`);
    if (!me.isConfiguring) {
      me.whenVisible(() => {
        me.doRefresh();
        me.scrollTo(me.date);
      });
    }
  }
  changeEventDots(eventDots) {
    const me = this;
    return new Proxy(eventDots, {
      set(target) {
        const result = Reflect.set(...arguments);
        me.updateEventDots(target);
        return result;
      }
    });
  }
  updateEventDots({ marginTop, gap, size, stripe }) {
    const { style, classList } = this.contentElement;
    style.setProperty("--event-count-margin-top", `${marginTop}px`);
    style.setProperty("--event-count-dot-size", `${size}px`);
    style.setProperty("--event-count-dot-gap", `${gap}px`);
    classList[stripe ? "add" : "remove"]("b-event-count-dots-striped");
    if (!this.isConfiguring) {
      this.refresh();
    }
  }
  updatemaxDots(maxDots) {
    VersionHelper.deprecate("calendar", "7.0.0", "maxDots property is deprecated in favor of eventDots");
    this.eventDots.max = maxDots;
    this.updateEventDots(this.eventDots);
  }
  updateShowWeekNumber() {
    this.bodyElement.classList[this.showWeekColumn ? "remove" : "add"](this.hideWeekColumnCls);
  }
  createCellData(date2) {
    this.month.month = date2.getMonth();
    return super.createCellData(date2);
  }
  // We must implement the CalendarMixin interface.
  // All views must expose a doRefresh method.
  doRefresh() {
    var _a5;
    const me = this, {
      bodyElement: targetElement,
      month: month2,
      dayCellCls,
      otherMonthCls,
      weekendCls,
      todayCls,
      nonWorkingDayCls,
      emptyCellCls,
      cellMap,
      showEvents,
      eventCountTip
    } = me, children = [], today = ((_a5 = me.calendar) == null ? void 0 : _a5.today) || /* @__PURE__ */ new Date(), todayKey = DateHelper.makeKey(today);
    for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
      month2.month = monthIndex;
      const monthKey = `${month2.year}-${String(month2.month + 101).substring(1)}`, monthEvents = [...cellMap.values()].filter((e) => e.key.startsWith(monthKey)).reduce(countEvents, 0), monthName = DateHelper.getMonthName(monthIndex), monthHeader = {
        tabIndex: 0,
        role: "link",
        className: "b-yearview-month-name",
        html: monthName,
        "aria-label": `${monthName} ${me.year}. ${me.L("eventCount", monthEvents)}`,
        dataset: {
          // Month names must not look like day cells which have data-date="YYYY-MM-DD"
          monthDate: DateHelper.makeKey(new Date(me.year, monthIndex, 1))
        }
      }, weeknameCells = [{
        className: "b-week-number-cell"
      }], weekNameRow = {
        "aria-hidden": true,
        className: "b-calendar-week b-calendar-weekdays",
        children: weeknameCells
      }, weeksGrid = {
        role: "grid",
        className: "b-calendar-weeks",
        children: []
      }, monthElement = {
        role: "presentation",
        className: "b-yearview-month",
        children: [monthHeader, weekNameRow, weeksGrid],
        dataset: {
          monthIndex
        }
      }, weeks = weeksGrid.children;
      children.push(monthElement);
      let cellIndex = 0;
      month2.eachWeek((week, dates, weekIndex, isLastWeek) => {
        var _a6;
        if (weeknameCells.length === 1) {
          for (let columnIndex = 0; columnIndex < 7; columnIndex++) {
            weeknameCells.push({
              className: {
                "b-yearview-weekday-cell": 1,
                [nonWorkingDayCls]: me.nonWorkingDays[month2.canonicalDayNumbers[columnIndex]]
              },
              text: DateHelper.format(dates[columnIndex], "d1")
            });
          }
        }
        const isFirstWeekOfYear = monthIndex === 0 && weekIndex === 0, isLastWeekOfYear = monthIndex === 11 && isLastWeek, weekElement = {
          role: "row",
          className: {
            "b-calendar-week": 1
          },
          dataset: {
            week: `${week[0]},${week[1]}`
          }
        }, dayCells = weekElement.children = [{
          role: "gridcell",
          className: "b-week-number-cell"
        }];
        weeks.push(weekElement);
        let weekEventCount = 0, otherMonthCells = 0;
        for (let columnIndex = 0; columnIndex < 7; columnIndex++) {
          const date2 = dates[columnIndex], isToday = me.isToday(date2), todayText = isToday ? me.L("L{Object.today}, ") : "", isOtherMonth = date2.getMonth() !== month2.month, key = DateHelper.makeKey(date2), day2 = date2.getDay(), cellData = !me.isConfiguring && cellMap.get(key), events = cellData == null ? void 0 : cellData.events, eventCount = isOtherMonth ? 0 : (events == null ? void 0 : events.length) || 0, showOverflow = eventCount, cell = {
            role: "gridcell",
            style: {},
            className: {
              "b-day-name": 1,
              [dayCellCls]: 1,
              [emptyCellCls]: !eventCount
            },
            dataset: {
              date: key,
              cellIndex,
              columnIndex
            }
          };
          weekEventCount += eventCount;
          let content = String(date2.getDate());
          Object.assign(cell.className, {
            [weekendCls]: DateHelper.weekends[day2],
            [todayCls]: key === todayKey && !isOtherMonth,
            [nonWorkingDayCls]: me.nonWorkingDays[day2],
            [otherMonthCls]: isOtherMonth,
            "b-cal-cell-overflow": showOverflow,
            [SchedulerDatePicker.getEventCountClass(eventCount)]: showOverflow,
            [`b-day-of-week-${day2}`]: 1
          });
          if (me.dayCellRenderer) {
            const result = me.callback(me.dayCellRenderer, me, [{ cellConfig: cell, date: date2, events: ((_a6 = cellData == null ? void 0 : cellData.events) == null ? void 0 : _a6.map((data) => data.eventRecord)) || [] }]);
            if (result != null) {
              content = result;
            }
          }
          if (eventCountTip && !isHoverGesture[me.overflowPopupTrigger]) {
            const btip = eventCount ? me.L("L{ResourceInfoColumn.eventCountText}", eventCount) : eventCountTip.showNoEvents ? me.L("L{noEvents}") : null;
            if (btip) {
              const tipData = Tooltip.encodeConfig(eventCountTip);
              cell.dataset = {
                ...cell.dataset,
                ...tipData,
                btip
              };
            }
          }
          if (showEvents === "dots") {
            const { max: max3 } = me.eventDots, children2 = [];
            for (let i = 0; i < Math.min(eventCount || 0, max3); i++) {
              const eventDomConfig = me.createEventDomConfig({
                date: date2,
                eventRecord: events[i].eventRecord,
                // If we only show one dot, we don't want to show whatever
                // the event colour is, we want to show the default dot colour.
                eventColor: max3 > 1 ? null : emptyString5,
                // renders without content with just background colour
                minimal: true
              });
              children2.push(eventDomConfig);
            }
            content = [typeof content === "object" ? content : {
              tag: "span",
              html: content
            }, {
              className: "b-cal-minimal-event-container",
              children: children2
            }];
          } else if (showEvents === "count") {
            const count = eventCount;
            content = [typeof content === "object" ? content : {
              tag: "span",
              html: content
            }, count ? {
              dataset: {
                count
              },
              class: {
                "b-cell-events-badge": 1
              },
              text: count
            } : null];
          }
          cell.children = [{
            className: "b-calendar-cell-inner",
            [typeof content === "string" ? "html" : "children"]: content
          }];
          if (isOtherMonth) {
            otherMonthCells++;
          } else {
            cell.children[0].role = "link";
            cell.children[0].tabIndex = -1;
            cell.children[0]["aria-label"] = `${todayText}${DateHelper.format(date2, "dddd Do MMMM")}, ${me.L("eventCount", eventCount)}`;
          }
          dayCells.push(cell);
          cellIndex++;
        }
        if (otherMonthCells === 7) {
          dayCells[0].text = week[1];
        } else {
          dayCells[0].children = [{
            className: "b-calendar-cell-inner",
            tabIndex: -1,
            role: "link",
            text: week[1],
            "aria-label": `${me.L("L{Calendar.weekOfYear}", week)}, ${me.L("eventCount", weekEventCount)}`
          }];
        }
        weekElement.className["b-other-month"] = otherMonthCells === 7 || otherMonthCells > 3 && !(isFirstWeekOfYear || isLastWeekOfYear) ? 1 : 0;
      });
    }
    DomSync.sync({
      targetElement,
      domConfig: {
        children
      }
    });
    me.refreshCount = (me.refreshCount || 0) + 1;
    me.trigger("refresh");
  }
  getDayElement(date2, strict) {
    if (typeof date2 !== "string") {
      date2 = DateHelper.makeKey(date2);
    }
    if (strict && parseInt(date2.substr(0, 4)) !== this.year) {
      return;
    }
    return this.contentElement.querySelector(`.b-calendar-cell[data-date="${date2}"]:not(.b-other-month)`);
  }
  // Override in this class to eliminate "other" month cells.
  getCell() {
    return this.getDayElement(...arguments);
  }
  onInternalPaint({ firstPaint }) {
    if (firstPaint) {
      this.refresh();
    }
    this.setTimeout(() => this.scrollTo(this.date, { animate: false }), 10);
  }
};
__publicField(YearView, "$name", "YearView");
__publicField(YearView, "type", "yearview");
YearView.initClass();
YearView._$name = "YearView";

// lib/Calendar/widget/ResourceView.js
var day0 = DateHelper.startOf(/* @__PURE__ */ new Date());
var emptyMap = Object.freeze(/* @__PURE__ */ new Map());
var scrollAxesConfigs = {
  hourHeight: 1,
  showAllDayHeader: 1,
  dayStartTime: 1,
  dayEndTime: 1
};
var sharedConfigs = ["range", "shiftIncrement", "allowOverlap", "timeFormat", "coreHours", "fitHours", "hourHeight", "visibleStartTime", "dateFormat", "dayStartTime", "dayEndTime", "hideNonWorkingDays", "nonWorkingDays", "readOnly", "zoomOnMouseWheel", "filterEventResources", "tickRenderer", "project", "eventRenderer", "dateFilter"];
var copyConfigs = (dest, source, props) => {
  if (source) {
    for (const prop of props) {
      if (!(prop in dest) && source[prop] !== void 0) {
        dest[prop] = source[prop];
      }
    }
  }
  return dest;
};
var notSpecialRow = (r) => !r.isSpecialRow;
var ResourceView = class extends Panel.mixin(CalendarMixin_default) {
  construct() {
    var _a5, _b2, _c2;
    super.construct(...arguments);
    (_c2 = (_b2 = (_a5 = this.calendar) == null ? void 0 : _a5.sidebar) == null ? void 0 : _b2.widgetMap.resourceFilter) == null ? void 0 : _c2.ion({
      change: "onResourceFilterSelectionChange",
      thisObj: this
    });
  }
  // This must appear to be a DateRangeOwner if it contains DateRangeOwners.
  get isDateRangeOwner() {
    return this.viewType.isDateRangeOwner;
  }
  // Likewise, it must expose the contained DateRangeOwners' interface method
  snapDate(date2) {
    var _a5, _b2;
    return ((_b2 = (_a5 = this.views[0]).snapDate) == null ? void 0 : _b2.call(_a5, ...arguments)) || date2;
  }
  // This view is animating if any of its child views are animating
  get isAnimating() {
    return super.isAnimating || this.items.some((v) => v.isAnimating);
  }
  changeScrollable(scrollable) {
    scrollable = super.changeScrollable(...arguments);
    if (scrollable) {
      Object.defineProperty(scrollable, "viewport", {
        get: this.getScrollingViewport.bind(this)
      });
    }
    return scrollable;
  }
  // The scrolling viewport calculation method injected as the getter for the `viewport`
  // property in our scrollable. It has to account for the time axis and the scroller
  // which are docked at left and right using position : sticky.
  getScrollingViewport() {
    const {
      dayViewTimeAxis,
      dayViewScroller
    } = this, scrollerWidth = dayViewScroller.isVisible ? dayViewScroller.width : 0, result = Rectangle.client(this.scrollable.element);
    return this.rtl ? result.adjust(scrollerWidth, 0, -dayViewTimeAxis.width, 0) : result.adjust(dayViewTimeAxis.width, 0, -scrollerWidth, 0);
  }
  changeView(view) {
    const me = this, result = new Proxy(ObjectHelper.assign({}, view), {
      set(target, prop, value2) {
        const result2 = Reflect.set(...arguments);
        me.syncViewConfig(prop, value2);
        return result2;
      },
      deleteProperty(target, prop) {
        const result2 = Reflect.deleteProperty(...arguments);
        me.syncViewConfig(prop, null);
        return result2;
      }
    });
    me.viewType = bryntum.Calendar.Modes.resolveType(view.type);
    return result;
  }
  /**
   * Executes the passed function for each child view corresponding to each resource in the `resourceStore`.
   * @param {Function} fn The function to call.
   * @param {Object[]} [args] The arguments to pass. Defaults to the view being called followed by its index.
   * @param {Object} [thisObj] The `this` reference for the function. Defaults to the view being called.
   */
  eachView(fn2, args, thisObj = null) {
    const passView = args == null, items2 = [...this.viewCache];
    for (let i = 0, { length } = items2; i < length; i++) {
      const view = items2[i];
      if (passView) {
        args = [view, i];
      }
      if (view.callback(fn2, thisObj || view, args) === false) {
        return;
      }
    }
  }
  /**
   * Yields the views which this ResourceView owns.
   * @property {Calendar.widget.mixin.CalendarMixin[]}
   * @readonly
   */
  get views() {
    return [...this.viewCache];
  }
  updateResourceStore(resourceStore) {
    var _a5;
    this.detachListeners("resourceViewResourceChange");
    (_a5 = super.updateResourceStore) == null ? void 0 : _a5.call(this, resourceStore);
    resourceStore.ion({
      name: "resourceViewResourceChange",
      change: "onResourceStoreChange",
      sort: "onResourceStoreSort",
      thisObj: this
    });
    this.refreshSoon();
  }
  updateViewGap(viewGap) {
    this.element.style.setProperty("--view-gap", DomHelper.setLength(viewGap || -1));
  }
  onResourceStoreChange() {
    this.refreshSoon();
  }
  onResourceStoreSort({ source }) {
    const visibleViews = this.items.filter((v) => v.isVisible && !v.isResourceDayViewTimeAxis), resourceIdOrder = source.map((r) => this.createViewId(r)), viewIdOrder = visibleViews.map((v) => v.id);
    if (!ObjectHelper.isEqual(resourceIdOrder, viewIdOrder)) {
      this.remove(...visibleViews);
      this.refreshSoon();
    }
  }
  onResourceFilterSelectionChange() {
    this.refreshSoon();
  }
  changeViewCache(viewCache, oldViewCache) {
    if (viewCache) {
      return new Bag();
    } else if (oldViewCache) {
      oldViewCache.forEach((v) => v.destroy());
      oldViewCache.clear();
    }
  }
  getResourceView(resource) {
    resource = resource.isResourceModel ? resource : this.resourceStore.getById(resource);
    return this.viewCache.get(this.createViewId(resource)) || this.createView(resource);
  }
  doRefresh() {
    var _a5, _b2, _c2;
    const me = this, { scrollBarWidth: scrollBarWidth2 } = DomHelper, {
      calendar,
      _items,
      resourceStore
    } = me, resourceFilter = calendar == null ? void 0 : calendar.widgetMap.resourceFilter, calendars = me.resourceStore.records.filter((c) => (!resourceFilter || resourceFilter.value.includes(c)) && notSpecialRow(c)), { length } = calendars, toAdd = [], toRemove = [], usedIds = {};
    me.contentElement.style.setProperty("--resource-count", length);
    if (!resourceStore.count) {
      const { weekStartDay } = me, monthStart = DateHelper.getFirstDateOfMonth(me.date), nextMonthStart = DateHelper.add(monthStart, 1, "month");
      me.eventStore.getEvents({
        startDate: DateHelper.add(monthStart, -(monthStart.getDay() - weekStartDay + 7) % 7, "d"),
        endDate: DateHelper.add(nextMonthStart, 7 - (nextMonthStart.getDay() - weekStartDay + 7) % 7, "d")
      });
    }
    me.refreshSoon.cancel();
    let hitCount = 0;
    for (let i = 0; i < length; i++) {
      const view = me.getResourceView(calendars[i]), isShown = _items.includes(view);
      if (resourceStore.getById(view.resourceId)) {
        view.title = me.titleTemplate(view.resource);
        if (isShown) {
          hitCount++;
        } else {
          toAdd.push(view);
        }
        usedIds[view.id] = 1;
      } else if (isShown) {
        toRemove.push(view);
      }
    }
    if (hitCount < _items.count) {
      me.eachView((view) => {
        if (!usedIds[view.id]) {
          toRemove.push(view);
        }
      });
      me.remove(toRemove);
    }
    if (me.viewType.isDayView) {
      if (!_items.includes(me.dayViewTimeAxis)) {
        toAdd.unshift(me.dayViewTimeAxis);
      }
      if (scrollBarWidth2 && !_items.includes(me.dayViewScroller)) {
        toAdd.push(me.dayViewScroller);
      }
    }
    const y = (_b2 = (_a5 = me.items[0]) == null ? void 0 : _a5.scrollable) == null ? void 0 : _b2.y;
    me.add(...toAdd);
    if (y != null) {
      toAdd.forEach((v) => {
        v.scrollable.element.scrollTop = y;
        v.removeListener("scrollToVisibleStartTime");
      });
    }
    if (toAdd.length || toRemove.length) {
      let lastView;
      for (let i = 0, { items: items2 } = me, { length: length2 } = items2; i < length2; i++) {
        const view = items2[i], { scrollable } = view;
        view.element.classList.remove("b-last-resource-view");
        if (!view.isResourceDayViewTimeAxis) {
          view.element.classList[i > 1 ? "remove" : "add"]("b-first-resource-view");
          lastView = view;
        }
        if (scrollable) {
          scrollable.clearPartners();
          if (i) {
            view.scrollable.addPartner(items2[i - 1].scrollable, "y");
          }
        }
        (_c2 = view.allDayEvents) == null ? void 0 : _c2.refresh();
      }
      if (lastView) {
        lastView.element.classList.add("b-last-resource-view");
      }
      if (me.viewType.isDayView) {
        const allDayRows = me.views.filter((v) => v.allDayEvents && v.isVisible), maxHeight = allDayRows.length && Math.max(...allDayRows.map((v) => v.allDayEvents.cellContentHeight));
        if (maxHeight) {
          _items.forEach((v) => {
            var _a6;
            (_a6 = v.allDayEvents) == null ? void 0 : _a6.setEventContentHeight(maxHeight, false, true);
          });
        }
      }
    }
    if (me.viewType.isDayView) {
      me.syncDayViewScrollerVisibility();
    }
    me.refreshCount = (me.refreshCount || 0) + 1;
    me.trigger("refresh");
  }
  // Override at this level. Child views process events
  onCalendarPointerInteraction() {
  }
  // Override at this level. Child views process data mutations
  onCalendarStoreChange() {
  }
  onChildAdd(child) {
    super.onChildAdd(child);
    FunctionHelper.before(child, "onConfigChange", "onChildViewConfigChange", this, { return: false });
  }
  onChildViewConfigChange({ name, value: value2, applied }) {
    const me = this;
    if (applied === 2) {
      if (!me.syncingChildViewConfigs && !me.syncingViewConfig && me.timeAxisConfigs.includes(name)) {
        if (!me.viewSyncConfigs) {
          me.viewSyncConfigs = {};
          me.queueMicrotask("flushViewSyncConfigs");
        }
        me.viewSyncConfigs[name] = value2;
      }
      if (name === "date") {
        me.setTimeout({ fn: () => me.date = value2, name: "syncDateWithChildView", cancelOutstanding: true, delay: 0 });
      }
    }
  }
  flushViewSyncConfigs() {
    var _a5;
    const me = this, { viewSyncConfigs } = me, props = ObjectHelper.copyProperties({}, viewSyncConfigs, ["startDate", "endDate"]);
    delete viewSyncConfigs.startDate;
    delete viewSyncConfigs.endDate;
    Object.assign(props, viewSyncConfigs);
    const items2 = new Set(me.items.concat([...me.viewCache]));
    me.syncingChildViewConfigs = true;
    items2.forEach((i) => i.setConfig(props));
    me.views.length && (me.date = (_a5 = me.views[0]) == null ? void 0 : _a5.date);
    me.syncingChildViewConfigs = me.viewSyncConfigs = false;
  }
  createViewConfig(resource) {
    const me = this, { id: resourceId } = resource, {
      viewCache,
      viewType
    } = me, configSource = [...viewCache][0] || me, config = Objects.merge({
      // All views must have a reference to the Calendar
      calendar: me.calendar,
      // IDs are matched so that a minimal Container update can be done
      id: me.createViewId(resource),
      cls: "b-resourceview-resource",
      // The view must know which resource it is showing.
      defaultCalendar: resourceId,
      // Resource is configured in initially because it may be needed during
      // configuration before we set a getter for it.
      resource,
      resourceId,
      includeTimeRanges: me.includeTimeRanges,
      weekStartDay: me.weekStartDay,
      parent: me,
      date: me.date,
      dateFormat: me.dateFormat,
      autoCreate: me.autoCreate,
      title: me.titleTemplate(resource),
      syncCalendarDate: me.syncCalendarDate,
      showCurrentTime: {
        ...me.showCurrentTime,
        showTime: false
      }
    }, me.view);
    copyConfigs(config, configSource, me.sharedConfigs);
    if (viewType.isDateRangeOwner && configSource._endDate) {
      config.endDate = configSource._endDate;
    }
    if (me.visibleStartTime) {
      config.visibleStartTime = me.visibleStartTime;
    }
    if (me.hasConfig("startDate")) {
      config.startDate = me.startDate;
      me.timeAxisConfigs.unshift("startDate");
    }
    if (viewType.isDayView) {
      if (config.allDayEvents !== null) {
        config.allDayEvents = {
          ...config.allDayEvents,
          fullWeek: false,
          resource
        };
      }
      if (DomHelper.scrollBarWidth) {
        config.scrollable = {
          overflowX: false,
          overflowY: "hidden-scroll"
        };
      }
    }
    return config;
  }
  createView(resource) {
    var _a5;
    const me = this, {
      resourceStore,
      viewCache
    } = me, { id: resourceId } = resource, config = me.createViewConfig(resource), result = bryntum.Calendar.Modes.create(config);
    if (!result.range) {
      me.timeAxisConfigs.unshift("endDate");
    }
    result.ion({
      catchAll: "onChildViewCatchAll",
      thisObj: me
    });
    result.element.removeAttribute("tabIndex");
    result.element.dataset.resourceId = resourceId;
    (_a5 = result.contentElement) == null ? void 0 : _a5.removeAttribute("tabIndex");
    Object.defineProperties(result, {
      // The item's weight is its store index if we are using stableResourceOrder
      weight: {
        get() {
          return me.stableResourceOrder ? resourceStore.allIndexOf(resourceId) : 0;
        }
      },
      // The item's resource should be dynamically accessed from the resourceStore
      resource: {
        get() {
          return resourceStore.getById(resourceId);
        }
      },
      // The item's eventFilter filters in events assigned to its resource
      eventFilter: {
        get() {
          return (e) => e.resources.includes(this.resource);
        }
      }
    });
    viewCache.add(result);
    me.trigger("viewCreate", { view: result });
    return result;
  }
  onChildViewCatchAll(e) {
    const me = this, { type } = e;
    if (!e.resourceRecord) {
      e.resourceRecord = e.source.resource;
    }
    if (type === "rangechange") {
      if (!me.triggerRangeChangeTimer) {
        me.triggerRangeChangeTimer = me.queueMicrotask(() => {
          me.triggerRangeChangeTimer = null;
          me.trigger(e.eventName, { ...e, source: me });
        });
      }
    } else if (type === "heightchange") {
      me.syncDayViewScrollerVisibility();
    } else if (type !== "paint") {
      me.trigger(e.eventName, e);
    }
  }
  //region Title
  changeAvatarRendering(config) {
    if (config) {
      return AvatarRendering.new({
        element: this.element
      }, config);
    }
  }
  titleTemplate(resource) {
    const me = this, { meta } = me, metaValue = typeof meta === "string" ? resource[meta] : meta == null ? void 0 : meta(resource), namedColor = DomHelper.isNamedColor(resource.eventColor) && resource.eventColor, hexColor = !namedColor && resource.eventColor;
    return {
      class: {
        "b-resourceview-title": 1,
        "b-has-meta": metaValue
      },
      children: [
        me.showAvatars && ObjectHelper.merge(
          {
            class: {
              [`b-sch-foreground-${namedColor}`]: namedColor
            },
            style: {
              color: hexColor || null
            },
            // Used for syncId in strict mode that Calendar uses.
            // Also gives easy access to the element from the view as `weekView.resourceAvatar`
            reference: "resourceAvatar"
          },
          me.getResourceAvatar(resource)
        ),
        {
          class: "b-resource-name",
          text: resource.name,
          reference: "resourceName"
        },
        metaValue && {
          class: "b-resource-meta",
          text: metaValue,
          reference: "resourceMeta"
        }
      ]
    };
  }
  //endregion
  // Override here because we need to delegate the request to the subview for the
  // event's resource
  getEventElement(eventRecord, date2 = eventRecord.startDate, resourceRecord) {
    var _a5;
    return (_a5 = this.getResourceView(eventRecord.resource || resourceRecord)) == null ? void 0 : _a5.getEventElement(eventRecord, date2);
  }
  /**
   * This creates a specially styled DayView which matches the view config (so that
   * dayStartTime, hourHeight etc. is synced) which shows nothing but its time axis.
   * This acts as the single visible time axis on the left.
   * @private
   */
  get dayViewTimeAxis() {
    const me = this;
    if (!me._dayViewTimeAxis) {
      const viewConfig = copyConfigs({}, me, me.timeAxisConfigs);
      copyConfigs(viewConfig, me.view, me.timeAxisConfigs);
      delete viewConfig.endDate;
      me._dayViewTimeAxis = ResourceDayViewTimeAxis.create({
        ...viewConfig,
        // Must always be at the beginning
        weight: -1,
        // This view must update its shape on data change but it won't contain any events.
        // All it has to do is keep its allDayEventsHeight in sync with all its siblings
        // which happens on refresh.
        project: me.project,
        type: "resourcedayviewtimeaxis",
        minWidth: 0,
        cls: "b-resource-dayview-timeaxis",
        startDate: me.date,
        scrollable: {
          overflowX: false,
          overflowY: "hidden-scroll"
        },
        // This toggles all the allDayEvents rows in synchrony
        onCornerClick() {
          const { expanded } = me.firstChild.allDayEvents, toRefresh = [];
          let drivingView = null;
          me.eachView(({ allDayEvents }) => {
            if (expanded) {
              if (!drivingView || allDayEvents.maxEventCount < drivingView.maxEventCount) {
                drivingView = allDayEvents;
              }
            } else {
              if (!drivingView || allDayEvents.maxEventCount > drivingView.maxEventCount) {
                drivingView = allDayEvents;
              }
            }
          });
          const r = drivingView.refreshCount;
          drivingView.expanded = !expanded;
          if (drivingView.refreshCount === r) {
            toRefresh.push(drivingView);
          }
          me.items.forEach((v) => {
            if (v !== drivingView) {
              const { allDayEvents } = v, r2 = allDayEvents.refreshCount;
              v.allDayEvents.expanded = !expanded;
              if (allDayEvents.refreshCount === r2) {
                toRefresh.push(allDayEvents);
              }
            }
          });
          for (let i = 0, { length } = toRefresh; i < length; i++) {
            toRefresh[i].doRefresh();
          }
        }
      }, me);
      ResizeMonitor.addResizeListener(me._dayViewTimeAxis.timeAxisElement, me.syncDayViewScrollerVisibility.bind(me));
    }
    return me._dayViewTimeAxis;
  }
  /**
   * This creates a specially styled DayView which matches the view config (so that
   * dayStartTime, hourHeight etc. is synced) which shows nothing but its scrollbar.
   * This acts as the single visible scrollbar on the right of the ResourceView.
   * We cannot have the last DayView `overflowY : true` because then its flexed width
   * being equal to the others, its content area would be <scrollBarWidth> narrower
   * than the others.
   * @private
   */
  get dayViewScroller() {
    const me = this;
    if (!me._dayViewScroller) {
      const viewConfig = copyConfigs({}, me, me.timeAxisConfigs);
      copyConfigs(viewConfig, me.view, me.timeAxisConfigs);
      delete viewConfig.endDate;
      me._dayViewScroller = ResourceDayViewTimeAxis.create({
        ...viewConfig,
        // Must always be at the end
        weight: 999,
        // This view must update its shape on data change but it won't contain any events.
        // All it has to do is keep its allDayEventsHeight in sync with all its siblings
        // which happens on refresh.
        project: me.project,
        type: "resourcedayviewtimeaxis",
        flex: `0 0 ${DomHelper.scrollBarWidth}px`,
        minWidth: DomHelper.scrollBarWidth,
        cls: "b-resource-dayview-scroller",
        startDate: day0,
        scrollable: {
          overflowX: false,
          overflowY: "scroll"
        }
      }, me);
      me._dayViewScroller.scrollable.addPartner(me.dayViewTimeAxis.scrollable, "y");
      me.monitorResize = true;
    }
    return me._dayViewScroller;
  }
  onElementResize() {
    super.onElementResize();
    if (DomHelper.scrollBarWidth) {
      this.syncDayViewScrollerVisibility();
    }
  }
  syncDayViewScrollerVisibility() {
    if (this._dayViewScroller) {
      const needsScroller = this.dayViewTimeAxis.scrollable.hasOverflow("y");
      if (this.dayViewScroller.isVisible !== needsScroller) {
        this.dayViewScroller[needsScroller ? "show" : "hide"]();
      }
    }
  }
  async scrollTo(target, options) {
    const { views } = this;
    if (target.isEventModel) {
      const owningView = views.find((v) => v.eventStore.includes(target));
      owningView == null ? void 0 : owningView.scrollTo(...arguments);
      target = owningView.getEventElement(target);
      if (target) {
        return this.scrollable.scrollIntoView(target, options);
      }
    }
    return views[0].scrollTo(...arguments);
  }
  createViewId(resource) {
    return `${this.id}-resource${this.config.view.type}-${resource.id}`;
  }
  updateDate(date2) {
    const me = this, {
      _dayViewTimeAxis
    } = me;
    me.suspendVisibility();
    me.viewCache.forEach((v) => {
      v.date = date2;
    });
    me.resumeVisibility();
    if (me.viewType.isDayView) {
      me.items.forEach((v) => {
        var _a5, _b2;
        (_b2 = v.allDayEvents) == null ? void 0 : _b2.setEventContentHeight((_a5 = v.allDayEvents) == null ? void 0 : _a5.cellContentHeight);
      });
      if (_dayViewTimeAxis) {
        _dayViewTimeAxis.showCurrentTime = me.showCurrentTime;
        _dayViewTimeAxis.date = date2;
      }
    }
  }
  updateResourceWidth(value2) {
    this.contentElement.style.setProperty("--resource-width", DomHelper.setLength(value2));
  }
  updateShowCurrentTime(showCurrentTime) {
    this.dayViewTimeAxis && (this.dayViewTimeAxis.showCurrentTime = { showTime: showCurrentTime == null ? void 0 : showCurrentTime.showTime });
    if (!this.isConfiguring) {
      this.syncViewConfig("showCurrentTime", {
        ...showCurrentTime,
        showTime: false
      });
    }
  }
  updateWeekStartDay(weekStartDay, oldWeekStartDay) {
    var _a5;
    (_a5 = super.updateWeekStartDay) == null ? void 0 : _a5.call(this, weekStartDay);
    if (!this.isConfiguring) {
      this.syncViewConfig("weekStartDay", weekStartDay);
    }
  }
  updateHideNonWorkingDays(value2) {
    if (!this.isConfiguring) {
      this.syncViewConfig("hideNonWorkingDays", value2);
    }
  }
  updateNonWorkingDays(value2) {
    if (!this.isConfiguring) {
      this.syncViewConfig("nonWorkingDays", value2);
    }
  }
  updateIncludeTimeRanges(value2) {
    if (!this.isConfiguring) {
      this.syncViewConfig("includeTimeRanges", value2);
    }
  }
  updateSyncCalendarDate(value2) {
    if (!this.isConfiguring) {
      this.syncViewConfig("syncCalendarDate", value2);
    }
  }
  syncViewConfig(configName, value2) {
    const update = (view) => {
      view[configName] = value2;
    };
    this.syncingViewConfig = true;
    if (scrollAxesConfigs[configName]) {
      this.items.forEach(update);
    } else {
      this.eachView(update);
    }
    this.syncingViewConfig = false;
  }
  descriptionRenderer() {
    var _a5;
    return ((_a5 = [...this.viewCache.items][0]) == null ? void 0 : _a5.description) || "No resources";
  }
  // Propagate to the views. It's a shared config, so a change to one will sync all others.
  updateRange(range) {
    if (!this.isConfiguring) {
      this.views[0] && (this.views[0].range = range);
    }
  }
  set startDate(startDate) {
    var _a5;
    const me = this;
    me._startDate = me.changeDate(startDate);
    if ((_a5 = me.views) == null ? void 0 : _a5.length) {
      const viewConfig = {
        startDate
      }, incomingEndDate = me.peekConfig("endDate");
      if (incomingEndDate) {
        viewConfig.endDate = incomingEndDate;
      }
      me.views[0].setConfig(viewConfig);
    }
  }
  get date() {
    var _a5;
    return ((_a5 = this.views[0]) == null ? void 0 : _a5.date) || this._date;
  }
  get startDate() {
    const { views } = this;
    return views.length ? views[0].startDate : this._startDate || this.date;
  }
  changeEndDate(endDate) {
    return this.changeDate(endDate);
  }
  updateEndDate(endDate) {
    var _a5;
    if ((_a5 = this.views) == null ? void 0 : _a5.length) {
      this.views[0].endDate = endDate;
    }
  }
  get endDate() {
    var _a5;
    const me = this, { viewType } = me;
    let config, endDate = ((_a5 = me.views[0]) == null ? void 0 : _a5.endDate) || me._endDate || (config = me.createViewConfig({ id: "dummy" })).endDate;
    if (!endDate) {
      const rawRange = config.range || viewType.$meta.config.range;
      endDate = DateHelper.add(me.startDate, viewType.prototype.parseRange(rawRange));
    }
    return endDate;
  }
  /**
   * Returns the value of the child view's {@link Calendar.widget.mixin.DateRangeOwner#property-fixedDuration} property.
   * @property {Boolean}
   * @readonly
   */
  get fixedDuration() {
    const { viewType } = this;
    if (viewType.isDayView) {
      const firstView = [...this.viewCache][0];
      if (firstView) {
        return firstView.fixedDuration;
      }
      const config = this.createViewConfig({ id: "dummy" });
      return !(config.range || config.endDate);
    }
    return !viewType.isDateRangeOwner;
  }
  set shiftIncrement(shiftIncrement) {
    (this.view || (this.view = {})).shiftIncrement = shiftIncrement;
  }
  get shiftIncrement() {
    const firstItem = [...this.viewCache][0];
    return firstItem == null ? void 0 : firstItem.shiftIncrement;
  }
  get stepUnit() {
    const firstItem = [...this.viewCache][0];
    return firstItem == null ? void 0 : firstItem.stepUnit;
  }
  get firstChild() {
    return this.items.filter((w) => !w.isResourceDayViewTimeAxis)[0];
  }
  set tickRenderer(tickRenderer) {
    (this.view || (this.view = {})).tickRenderer = tickRenderer;
  }
  set hourHeight(h) {
    (this.view || (this.view = {})).hourHeight = h;
  }
  get hourHeight() {
    var _a5, _b2, _c2;
    return ((_b2 = (_a5 = this.views) == null ? void 0 : _a5[0]) == null ? void 0 : _b2.hourHeight) || ((_c2 = this.view) == null ? void 0 : _c2.hourHeight) || DayView.configurable.hourHeight;
  }
  previous() {
    var _a5;
    (_a5 = [...this.viewCache][0]) == null ? void 0 : _a5.previous();
  }
  next() {
    var _a5;
    (_a5 = [...this.viewCache][0]) == null ? void 0 : _a5.next();
  }
  // If we encapsulate DayViews, zooming must be available at the mode level
  zoomTo(...args) {
    var _a5, _b2;
    if ((_b2 = (_a5 = this.views) == null ? void 0 : _a5[0]) == null ? void 0 : _b2.zoomTo) {
      this.eachView((v) => {
        v.isVisible && v.zoomTo(...args);
      });
    }
  }
  zoomBy(...args) {
    var _a5, _b2;
    if ((_b2 = (_a5 = this.views) == null ? void 0 : _a5[0]) == null ? void 0 : _b2.zoomBy) {
      this.eachView((v) => {
        v.isVisible && v.zoomBy(...args);
      });
    }
  }
  getDateFromPosition(x, y) {
    for (const view of this.views) {
      const date2 = view.getDateFromPosition(x, y);
      if (date2) {
        return date2;
      }
    }
  }
};
__publicField(ResourceView, "$name", "ResourceView");
__publicField(ResourceView, "type", "resourceview");
__publicField(ResourceView, "configurable", {
  layout: "box",
  textContent: false,
  scrollable: {
    overflowX: true
  },
  date: {
    $config: {
      equal: "date"
    },
    value: null
  },
  endDate: {
    $config: {
      equal: "date"
    },
    value: null
  },
  /**
   * A function, or the name of a function in the ownership hierarchy which you
   * can specify to customize event DOM content.
   *
   * This function is called each time an event is rendered to allow developers to mutate
   * the cell metadata, or the CSS classes to be applied to the event element.
   *
   * It's called with the event record, and a eventData object which allows you to mutate event metadata
   * such as `cls`, `style`.
   *
   * The `cls` property is an object whose property names will be added to the event element if the property
   * value is truthy.
   *
   * The `style` property is an object containing style properties for the event element.
   *
   * A non-null return value from the renderer is used as the event body content. A nullish
   * return value causes the default renderer to be used which just uses the event name.
   *
   * If a string is returned, it is used as the HTML content of the event body element.
   *
   * If an object is returned, it is used as a {@link Core.helper.DomHelper#typedef-DomConfig} object to
   * create complex content in the event body element.
   *
   * <div class="note">
   * You should never modify any records inside this method.
   * </div>
   *
   * ```javascript
   *  eventRenderer({ eventRecord, renderData }) {
   *      if (eventRecord.name === 'Doctors appointment') {
   *          renderData.style.fontWeight = 'bold';
   *          renderData.cls['custom-cls'] = 1;
   *
   *          return 'Special doctors appointment';
   *      }
   *  }
   * ```
   *
   * IMPORTANT: When returning content, be sure to consider how that content should be encoded to avoid XSS
   * (Cross-Site Scripting) attacks. This is especially important when including user-controlled data such as
   * the event's `name`. The function {@link Core.helper.StringHelper#function-encodeHtml-static} as well as
   * {@link Core.helper.StringHelper#function-xss-static} can be helpful in these cases.
   *
   * For example:
   *
   * ```javascript
   *  eventRenderer({ eventRecord }) {
   *      return StringHelper.xss`Event: ${eventRecord.name}`;
   *  }
   * ```
   *
   * @param {Object} detail An object that contains data about the event being rendered
   * @param {Calendar.widget.mixin.CalendarMixin} detail.view The view rendering the event
   * @typings detail.view -> {typeof CalendarMixin}
   * @param {Scheduler.model.EventModel} detail.eventRecord The event record
   * @param {Scheduler.model.ResourceModel} detail.resourceRecord The Resource record
   * @param {Object} detail.renderData A data object containing properties that will be used to create the event element
   * @param {Object} detail.renderData.style The style property is an object containing style properties for the event element
   * @param {Object} detail.renderData.cls The cls property is an object whose property names will be added to
   * the event element if the property value is truthy
   * @param {Object} detail.renderData.iconStyle The iconStyle property is an object containing style properties for
   * the icon element if an icon element is to be used
   * @param {Object} detail.renderData.iconCls The iconCls property is an object whose property names will be added to
   * the icon element. Initially set from the event record's {@link Scheduler.model.EventModel#field-iconCls}.
   * Can be mutated by the renderer. If null, or no properties are set, no icon will be rendered
   * @param {String} detail.renderData.eventColor Color to be applied to the event
   * @param {Object} detail.renderData.dataset An object which produces the `dataset` of the resulting event bar
   * @param {Boolean} detail.renderData.solidBar This is valid for views which create event bars.
   * This is set to `true` by default for all day and interday events so that these appear as a solid block of background color.
   * An eventRenderer may mutate this flag to change in what manner the event bar is coloured - as a solid bar of colour,
   * or using the foreground colour (text and icons) such as the MonthView, the CalendarRow (all day events in a DayView), and OverflowPopups
   * @param {String} detail.renderData.bodyColor When used in a {@link Calendar.widget.DayView},
   * this color is applied to the body of the event element. Note that this must be light enough
   * that the text colour (From the SASS variable `$dayview-event-color`) is visible
   * @param {Boolean} detail.renderData.showBullet If there is no `iconCls`, and the event is not recurring, then by default
   * a "bullet" circular icon is shown if the view's {@link Calendar.widget.mixin.CalendarMixin#config-showBullet}
   * if set. Setting this property in an event renderer overrides this behaviour.
   * @returns {DomConfig|DomConfig[]|String|null}
   * @config {Function|String}
   */
  eventRenderer: null,
  /**
   * A config object used to configure the sub views. The default `type` used is `'weekview'`
   *
   * After instantiation, while in use, changes to the `view` property, will be propagated
   * to all child {@link #config-view views}.
   *
   * This property may be used to adjust properties of every child view of the resource
   * view in one statement.
   * @prp {ContainerItemConfig|Object} view
   * @typings {ContainerItemConfig|Record<string, any>}
   */
  view: {
    type: "weekview"
  },
  /**
   * The space to leave between each subview.
   * @prp {String|Number} viewGap
   * @default 0.5em
   */
  viewGap: "0.5em",
  /**
   * By default, the resource views are displayed in the order that the resources appear
   * in the project's `resourceStore`, so they will appear in a stable position, meaning
   * when a view is filtered out, then filtering it in will replace it in the same position.
   *
   * If this config is `false`, re-adding a view will place it at the end.
   * @config {Boolean}
   * @default
   */
  stableResourceOrder: true,
  /**
   * The width of a resource calendar panel (view always stretches to fill window)
   * @member {Number|String} resourceWidth
   */
  /**
   * The width of a resource calendar panel (view always stretches to fill window)
   * @config {Number|String}
   */
  resourceWidth: null,
  /**
   * Set to `true` to hide non-working days
   * @member {Boolean} hideNonWorkingDays
   */
  /**
   * Set to `true` to hide non-working days
   * @config {Boolean}
   */
  hideNonWorkingDays: null,
  viewCache: {
    $config: ["lazy", "nullify"],
    value: true
  },
  avatarRendering: {},
  /**
   * A field name or a function returning a string to be displayed below resource name in the resource view
   * headers.
   *
   * ```javascript
   * const calendar = new Calendar({
   *     modes : {
   *         // Mode name can be anything if it contains a "type" property.
   *         weekResources : {
   *             type  : 'resource',
   *             // Get meta string to display
   *             meta : resource => resource.title
   *         }
   *     }
   * });
   * ```
   *
   * @config {String|Function}
   * @param {Scheduler.model.ResourceModel} resource Displayed resource
   * @returns {String} Text to be displayed below resource name
   */
  meta: null,
  /**
   * Display an avatar in the resource view headers, either as an image or using resource initials.
   *
   * Looks for an image name in the {@link Scheduler/model/ResourceModel#field-imageUrl} and
   * {@link Scheduler/model/ResourceModel#field-image} fields on the resource. Set
   * {@link Calendar/view/Calendar#config-resourceImagePath} on Calendar to specify where to load images from.
   * If no image is found, resource initials are displayed.
   *
   * @config {Boolean}
   * @default
   */
  showAvatars: true,
  /**
   * When {@link Scheduler.model.ResourceTimeRangeModel}s are included in the data, they
   * are only rendered in subviews of {@link Calendar.widget.ResourceView}s and normal
   * {@link Calendar.model.TimeRangeModel}s are __not__ rendered in subviews of
   * {@link Calendar.widget.ResourceView}s.
   *
   * Set this to `true` to render normal {@link Calendar.model.TimeRangeModel}s in subviews of
   * this view.
   * @config {Boolean}
   * @default false
   */
  includeTimeRanges: null,
  // All subviews will agree with this Widget in terms of responsiveness.
  // If this says medium, all child views and the timeaxes will match.
  // This is important in keeping heights synced across all views.
  responsiveRoot: true,
  /**
   * __If__&nbsp;the displayed {@link #config-view} is a {@link Calendar.widget.mixin.DateRangeOwner},
   * this may be used to set the {@link Calendar.widget.mixin.DateRangeOwner#config-range}
   * of all child views.
   * @prp {String|DurationConfig}
   */
  range: null
});
// Use config system properties because these are *always available, including during construction.
__publicField(ResourceView, "properties", {
  sharedConfigs,
  timeAxisConfigs: ["allDayEvents"].concat(sharedConfigs)
});
var ResourceDayViewAllDayEvents = class extends CalendarRow {
  // The timeAxis's allDayEvents has overflow if any of the
  // real sibling DayView allDayEvents has overflow
  get hasOverflow() {
    return this.up("resourceview").views.some((v) => {
      var _a5;
      return (_a5 = v.allDayEvents) == null ? void 0 : _a5.hasOverflow;
    });
  }
  createCellMap() {
    return emptyMap;
  }
};
__publicField(ResourceDayViewAllDayEvents, "$name", "ResourceDayViewAllDayEvents");
// Factoryable type name
__publicField(ResourceDayViewAllDayEvents, "type", "resourcedayviewalldayevents");
var ResourceDayViewTimeAxis = class extends DayView {
  compose() {
    const result = super.compose();
    return result;
  }
  onCalendarStoreChange() {
  }
  doRefresh() {
    this.syncCurrentTimeIndicators();
  }
  // These axes have no say in this
  scrollToVisibleStartTime() {
  }
  createCellMap() {
    return emptyMap;
  }
  calculateCellContentHeight() {
    return 0;
  }
  getDateFromPosition(clientX, clientY, local = false) {
    const me = this, date2 = me.startDate, top = local ? 0 : me.dayContentElement.getBoundingClientRect().y, height = me.timeAxisElement.offsetHeight, dy = clientY - top;
    return DateHelper.round(
      DateHelper.add(DateHelper.clearTime(date2), me.dayStartMs + Math.floor(dy / height * me.getDayLength()), "ms"),
      me.increment
    );
  }
};
__publicField(ResourceDayViewTimeAxis, "$name", "ResourceDayViewTimeAxis");
// Factoryable type name
__publicField(ResourceDayViewTimeAxis, "type", "resourcedayviewtimeaxis");
__publicField(ResourceDayViewTimeAxis, "configurable", {
  range: "1 day",
  allDayEvents: {
    type: "resourcedayviewalldayevents",
    disabled: "inert"
  }
});
ResourceDayViewAllDayEvents.initClass();
ResourceDayViewTimeAxis.initClass();
ResourceView.initClass();
ResourceView._$name = "ResourceView";

// lib/Calendar/widget/DayResourceCalendarRow.js
var notSpecialRow2 = (r) => !r.isSpecialRow;
var DayResourceCalendarRow = class extends CalendarRow {
  get eventContentElement() {
    return this.bodyElement;
  }
  // Dragging can drag to the resource header cells. This view does not need to open
  // up temporarily to accept a drop gesture
  expandGutter() {
  }
  get viewResources() {
    var _a5;
    const me = this, { resourceStore } = me, resourceFilter = (_a5 = me.calendar) == null ? void 0 : _a5.widgetMap.resourceFilter;
    return resourceStore.records.filter((c) => (!resourceFilter || resourceFilter.value.includes(c)) && notSpecialRow2(c));
  }
  onCellOverflowGesture({ resourceRecord, date: date2 }) {
    const cellData = this.cellMap.get(DateHelper.makeKey(date2)), { renderedEvents } = cellData;
    cellData.renderedEvents = renderedEvents.filter((e) => {
      var _a5;
      return (_a5 = e.eventRecord.resources) == null ? void 0 : _a5.includes(resourceRecord);
    });
    super.onCellOverflowGesture(...arguments);
    cellData.renderedEvents = renderedEvents;
  }
  getCellFromEvent(domEvent) {
    return domEvent.target.closest(".b-calendar-cell[data-date] [data-resource-id]");
  }
  createCellMap() {
    const cellMap = super.createCellMap(...arguments), { viewResources } = this;
    this.maxEventCount = 0;
    cellMap.forEach((cellData) => {
      for (let i = 0, { length } = viewResources; i < length; i++) {
        this.maxEventCount = Math.max(this.maxEventCount, cellData.renderedEvents.filter((e) => {
          var _a5;
          return (_a5 = e.eventRecord.resources) == null ? void 0 : _a5.includes(viewResources[i]);
        }).length);
      }
    });
    return cellMap;
  }
  getCellDomConfig(cellData) {
    var _a5, _b2;
    const me = this, {
      viewResources,
      eventsPerCell,
      showHeaderAvatars,
      hideEmptyResources,
      resourceDateFilter,
      resourceHeaderRenderer,
      resourceHeaderExtraRenderer
    } = me, {
      renderedEvents,
      date: date2
    } = cellData, bodyChildren = [], today = me.dayTime.startOfDay(((_a5 = me.calendar) == null ? void 0 : _a5.dateTimeNow) || /* @__PURE__ */ new Date()), isToday = date2.getTime() === today.getTime(), {
      maxRow,
      day: day2
    } = cellData, nonWorkingDays = me.nonWorkingDays || me.month.nonWorkingDays, dayEvents = hideEmptyResources && (((_b2 = me.owner) == null ? void 0 : _b2.getEventsForDay(cellData.key, me.startDate)) || renderedEvents), visibleResources = viewResources.reduce((result2, resource) => {
      var _a6;
      cellData.resource = resource;
      const resourceEvents = (_a6 = dayEvents == null ? void 0 : dayEvents.filter) == null ? void 0 : _a6.call(dayEvents, (e) => {
        var _a7;
        return (_a7 = e.resources) == null ? void 0 : _a7.includes(resource);
      }), skipResource = hideEmptyResources && !(resourceEvents == null ? void 0 : resourceEvents.length) || resourceDateFilter && !me.callback(resourceDateFilter, me, [cellData]);
      if (!skipResource) {
        result2.push(resource);
      }
      return result2;
    }, []);
    me.weekLength = 1;
    for (let i = 0, { length } = visibleResources; i < length; i++) {
      const resource = visibleResources[i];
      cellData.renderedEvents = renderedEvents.filter((e) => {
        var _a6;
        return (_a6 = e.eventRecord.resources) == null ? void 0 : _a6.includes(resource);
      });
      cellData.visibleColumnIndex = cellData.columnIndex = 0;
      cellData.maxRow = cellData.renderedEvents.length > eventsPerCell ? maxRow : eventsPerCell;
      const dayCell = super.getCellDomConfig(cellData);
      dayCell.children[1].dataset = {
        resourceId: resource.id
      };
      bodyChildren.push({
        className: "b-resourcecalendarrow-column-resource-cell",
        dataset: {
          resourceId: resource.id
        },
        children: [
          dayCell.children[1],
          // Add the overflow button
          cellData.hasOverflow && cellData.renderedEvents.length > eventsPerCell ? dayCell.children[2] : null
        ]
      });
    }
    if (!bodyChildren.length) {
      bodyChildren.push({
        className: "b-resourcecalendarrow-column-resource-cell"
      });
    }
    cellData && (cellData.renderedEvents = renderedEvents);
    const result = {
      role: "gridcell",
      tabIndex: -1,
      className: {
        "b-dayresourcecalendarrow-column": 1
      },
      style: {
        "--visible-resource-count": visibleResources.length
      },
      children: [
        {
          style: {
            // This element is extracted into the event content element
            // so it does not inherit --visible-resource-count in the final DOM structure
            "--visible-resource-count": visibleResources.length
          },
          role: "gridcell",
          tabIndex: -1,
          className: {
            "b-dayresource-allday": 1,
            "b-cal-cell-header": 1,
            [me.todayCls]: isToday,
            [me.nonWorkingDayCls]: nonWorkingDays[day2] || false,
            [me.weekendCls]: DateHelper.weekends[day2],
            [`b-day-of-week-${day2}`]: 1
          },
          children: [{
            className: {
              "b-dayname-date": 1
            }
          }, ...visibleResources.length ? visibleResources.map((resource) => {
            let dayResourceCellData;
            if (resourceHeaderRenderer || resourceHeaderExtraRenderer) {
              const resourceDayEvents = resource.events.filter((eventRecord) => DateHelper.intersectSpans(eventRecord.startDate, eventRecord.endDate, date2, cellData.tomorrow));
              dayResourceCellData = { ...cellData, resourceDayEvents };
            }
            return {
              className: {
                "b-dayresourcecalendarrow-resource-header": 1,
                "b-avatar-after": showHeaderAvatars === "last",
                "b-has-info": Boolean(resourceHeaderExtraRenderer)
              },
              html: resourceHeaderRenderer ? me.callback(resourceHeaderRenderer, me, [resource, dayResourceCellData]) : void 0,
              children: resourceHeaderRenderer ? void 0 : [
                showHeaderAvatars ? me.getResourceAvatar(resource) : null,
                {
                  className: "b-dayresourcecalendarrow-resource-name",
                  text: me.resourceNameRenderer(resource)
                },
                resourceHeaderExtraRenderer ? {
                  className: "b-dayresourcecalendarrow-resource-info",
                  html: me.callback(resourceHeaderExtraRenderer, me, [resource, dayResourceCellData])
                } : void 0
              ],
              dataset: {
                resourceId: resource.id
              }
            };
          }) : [me.dummyResourceHeader]]
        },
        ...bodyChildren
      ],
      dataset: {}
    };
    me.addCellHeaderContent(result.children[0].children[0], cellData);
    return result;
  }
  get dummyResourceHeader() {
    return {
      className: {
        "b-dayresourcecalendarrow-resource-header": 1
      },
      children: [
        this.showHeaderAvatars ? {
          class: "b-resource-avatar"
        } : null,
        {
          className: "b-dayresourcecalendarrow-resource-name",
          text: "\xA0"
        }
      ]
    };
  }
  updateMinResourceWidth(minResourceWidth) {
    const {
      scrollable,
      headerScroller
    } = this;
    this.element.style.setProperty("--cell-container-overflow", minResourceWidth ? "visible" : "hidden");
    if (minResourceWidth) {
      scrollable.overflowX = headerScroller.overflowX = "hidden-scroll";
    } else {
      scrollable.overflowX = headerScroller.overflowX = false;
    }
  }
  resourceNameRenderer(resource) {
    return resource.name;
  }
  updateShowHeaderAvatars(showHeaderAvatars) {
    if (showHeaderAvatars) {
      this.avatarRendering || (this.avatarRendering = true);
    }
    this.refresh();
  }
  // This is never fullWeek
  changeFullWeek() {
  }
};
__publicField(DayResourceCalendarRow, "$name", "DayResourceCalendarRow");
// Factoryable type name
__publicField(DayResourceCalendarRow, "type", "dayresourcecalendarrow");
__publicField(DayResourceCalendarRow, "configurable", {
  /**
   * Configure as `true` to show avatars of the resources (calendars) alongside
   * resource names in resource column headers.
   *
   * Configure as `'last'` to show avatars of the assigned resources (calendars) after the name.
   *
   * Note that you must set {@link #config-resourceImagePath} in order that the system
   * knows where to access the resource's image file from.
   *
   * If no image is set, or the image is not found, the resource's initials are shown instead.
   *
   * By default, it is inherited from the owning Calendar:
   * ```javascript
   * new Calendar({
   *     resourceImagePath   : 'images/resources/'
   *     modes : {
   *         dayresource : {
   *             showHeaderAvatars : true,
   *         }
   *     }
   * });
   * ```
   * @config {Boolean|'last'}
   * @default
   */
  showHeaderAvatars: true,
  /**
   * A function, or name of a function which lets you fully replace the HTML shown in the resource header.
   * By default, a resource avatar and the resource name is shown.
   *
   * ```javascript
   * const calendar = new Calendar({
   *     appendTo : 'container',
   *     date     : new Date(2025, 3, 1),
   *
   *     mode  : 'dayresource',
   *     modes : {
   *         dayresource : {
   *             allDayEvents : {
   *                 // Render custom HTML into the resource header.
   *                 resourceHeaderRenderer(resource, { view : { owner }, key }) {
   *                     return `<strong>${resource.name}</strong>`;
   *                 }
   *             }
   *         }
   *     }
   * });
   * ```
   *
   * @config {Function|String} resourceHeaderRenderer
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource record
   * @param {DayCell} cellData A `DayCell` object that contains data about the cell
   * @returns {String}
   */
  resourceHeaderRenderer: null,
  /**
   * A function, or name of a function which renders extra content below the resource avatar/name.
   *
   * ```javascript
   * const calendar = new Calendar({
   *     appendTo : 'container',
   *     date     : new Date(2025, 3, 1),
   *
   *     mode  : 'dayresource',
   *     modes : {
   *         dayresource : {
   *             allDayEvents : {
   *                 // Render some extra summary content in the area below each resource header.
   *                 resourceHeaderExtraRenderer(resource, { resourceDayEvents }) {
   *                     const
   *                         [dayValue, dayTravelTime] = resourceDayEvents.reduce((v, eventRecord) => {
   *                             v[0] += eventRecord.value || 0;
   *                             v[1] += eventRecord.travelTime || 0;
   *                             return v;
   *                         }, [0, 0]);
   *
   *                     return `
   *                         <div class="header">Appointments</div>
   *                         <div class="value appointments">${resourceDayEvents.length}</div>
   *                         <div class="header">Route Value</div>
   *                         <div class="value dayValue">$${dayValue}</div>
   *                         <div class="header">Travel Time</div>
   *                         <div class="value travelTime">${DateHelper.format(new Date(2024, 0, 1, 0, dayTravelTime), 'H:mm') || ''}</div>
   *                    `;
   *                 },
   *             }
   *         }
   *     }
   * });
   * ```
   *
   * @config {Function|String} resourceHeaderExtraRenderer
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource record
   * @param {Scheduler.model.EventModel[]} eventRecords The events for the passed resource for the date column.
   * @param {DayCell} cellData An object that contains data about the cell
   * @returns {String}
   */
  resourceHeaderExtraRenderer: null,
  responsive: {
    // We can use "large" formats here because the cell will be wide
    // to accommodate multiple resources.
    small: {
      dayNameFormat: "ddd",
      dayNumberCompress: false
    }
  },
  autoRefresh: [
    "hideEmptyResources",
    "resourceDateFilter"
  ],
  /**
   * Configure this as `true` to hide resource columns which contain no events.
   *
   * <div class="note">Use with care. This may result in no resource columns being rendered
   * for completely empty dates.</div>
   * @prp {Boolean}
   * @default false
   */
  hideEmptyResources: null,
  /**
   * A callback function which an application may provide to filter out resource columns for
   * particular dates.
   * @prp {Function|String} resourceDateFilter A function, or the name of a function in the ownership hierarchy.
   * @param {DayCell} context Information about the day column to be created.
   * @returns {Boolean} Return `false` to not display a column for the passed resource at the passed date.
   */
  resourceDateFilter: null
});
DayResourceCalendarRow.initClass();
DayResourceCalendarRow._$name = "DayResourceCalendarRow";

// lib/Calendar/widget/DayResourceView.js
var notSpecialRow3 = (r) => !r.isSpecialRow;
var DayResourceView = class extends DayView {
  changeAllDayEvents() {
    const result = super.changeAllDayEvents(...arguments);
    if (result) {
      result.hideEmptyResources = this.hideEmptyResources;
      result.resourceDateFilter = this.resourceDateFilter;
    }
    return result;
  }
  checkAutoCreateGesture(domEvent, date2, resourceRecord) {
    if (resourceRecord) {
      return super.checkAutoCreateGesture(...arguments);
    }
  }
  // Called as an onFunction by updateElementLayout triggering 'layoutUpdate'.
  onLayoutUpdate() {
    var _a5;
    this.syncNarrowResourceWidth();
    (_a5 = super.onLayoutUpdate) == null ? void 0 : _a5.call(this, ...arguments);
  }
  onDayContainerResize() {
    var _a5;
    this.syncNarrowResourceWidth();
    return (_a5 = super.onDayContainerResize) == null ? void 0 : _a5.call(this, ...arguments);
  }
  syncNarrowResourceWidth() {
    const resourceHeader = this.element.querySelector(".b-dayresourcecalendarrow-resource-header");
    if (resourceHeader) {
      this.allDayEvents.element.classList.toggle("b-narrow-resource-header", resourceHeader.offsetWidth < 100);
    }
  }
  updateHideResourceNameWhenNarrow(hideResourceNameWhenNarrow) {
    var _a5;
    (_a5 = this.allDayEvents) == null ? void 0 : _a5.element.classList.toggle("b-hide-resource-name-when-narrow", Boolean(hideResourceNameWhenNarrow));
  }
  updateHideEmptyResources(hideEmptyResources) {
    if (!this.isConfiguring) {
      this._allDayEvents && (this._allDayEvents.hideEmptyResources = hideEmptyResources);
    }
  }
  updateResourceDateFilter(resourceDateFilter) {
    if (!this.isConfiguring) {
      this._allDayEvents && (this._allDayEvents.resourceDateFilter = resourceDateFilter);
    }
  }
  changeMinResourceWidth(minResourceWidth) {
    return minResourceWidth || this.constructor.configurable.minResourceWidth;
  }
  updateMinResourceWidth(minResourceWidth) {
    var _a5;
    this.element.style.setProperty("--min-resource-width", DomHelper.setLength(minResourceWidth));
    this.updateElementLayout();
    (_a5 = this.allDayEvents) == null ? void 0 : _a5.updateMinResourceWidth(minResourceWidth);
  }
  updateShowHeaderAvatars(showHeaderAvatars) {
    const { allDayEvents: a } = this;
    if (a) {
      a.resourceImagePath = this.resourceImagePath;
      a.showHeaderAvatars = showHeaderAvatars;
    }
  }
  updateWeekStartDay(weekStartDay, oldWeekStartDay) {
    var _a5;
    (_a5 = super.updateWeekStartDay) == null ? void 0 : _a5.call(this, weekStartDay);
    if (!this.isConfiguring) {
      const startDate = new Date(this.startDate);
      if (this.range.unit === "week") {
        startDate.setDate(startDate.getDate() + weekStartDay - oldWeekStartDay);
        this.startDate = startDate;
      }
    }
  }
  // Override from DayView.
  // This returns a DayView day column config for each resource
  getDayDomConfig(date2) {
    const me = this, {
      hideEmptyDays,
      hideEmptyResources,
      dateFilter,
      resourceDateFilter
    } = me, key = me.dayTime.dateKey(date2), day2 = date2.getDay(), cellData = me.cellMap.get(key) || me.createCellData(date2), eventsForDate = (hideEmptyDays || hideEmptyResources) && me.getEventsForDay(key), skipDay = me.hiddenNonWorkingDays[day2] && me.duration !== 1 || me.hideEmptyDays && !me.getEventsForDay(key).length || dateFilter && !me.callback(dateFilter, me, [cellData]);
    if (!skipDay) {
      const key2 = me.dayTime.dateKey(date2), events = cellData.events, children = [], {
        calendar,
        resourceStore
      } = me, resourceFilter = calendar == null ? void 0 : calendar.widgetMap.resourceFilter, resources = resourceStore.records.filter((c) => (!resourceFilter || resourceFilter.value.includes(c)) && notSpecialRow3(c));
      for (let i = 0, { length } = resources, dayColumn; i < length; i++) {
        const resource = me.resource = cellData.resource = resources[i], filterResources = (e) => {
          var _a5;
          return (_a5 = e.resources) == null ? void 0 : _a5.includes(resource);
        }, skipResource = hideEmptyResources && !eventsForDate.filter(filterResources).length || resourceDateFilter && !me.callback(resourceDateFilter, me, [cellData]);
        me.resourceId = resource.id;
        cellData.events = events.filter(filterResources);
        me._hideEmptyDays = false;
        if (!skipResource) {
          dayColumn = super.getDayDomConfig(date2);
          delete dayColumn.dataset.date;
          dayColumn.dataset.resourceId = resource.id;
          children.push(dayColumn);
        }
        me.resource = me.resourceId = null;
      }
      if (!children.length) {
        cellData.events.length = 0;
        const dayColumn = super.getDayDomConfig(date2);
        delete dayColumn.dataset.date;
        children.push(dayColumn);
      }
      cellData.events = events;
      me._hideEmptyDays = hideEmptyDays;
      return {
        className: {
          "b-dayresourceview-column": 1,
          [`b-day-of-week-${cellData.day}`]: 1
        },
        dataset: {
          date: key2
        },
        children
      };
    }
  }
};
__publicField(DayResourceView, "$name", "DayResourceView");
__publicField(DayResourceView, "type", "dayresource");
__publicField(DayResourceView, "configurable", {
  /**
   * The minimum width of a resource column. This must be a non-zero value to ensure that space
   * is allocated even when there are many resources and a narrow viewport.
   *
   * The default value is `4em`
   *
   * If the day columns columns overflow the horizontal space available, the columns
   * will be scrollable horizontally in the normal way.
   *
   * There is a {@link #property-horizontalScroller} property which handles scrolling in this
   * dimension.
   * @prp {Number|String}
   */
  minResourceWidth: "4em",
  /**
   * By default, resource avatars are shown in the resource header if there is horizontal space.
   * Configure this as `false` to inhibit this.
   * @prp {Boolean}
   */
  showAvatars: true,
  title: "L{dayResourceView}",
  allDayEvents: {
    type: "dayresourcecalendarrow"
  },
  range: "1w",
  /**
   * Configure as `true` to show avatars of the resources (calendars) alongside
   * resource names in resource column headers.
   *
   * Configure as `'last'` to show avatars of the assigned resources (calendars) after the name.
   *
   * Note that you must set {@link #config-resourceImagePath} in order that the system
   * knows where to access the resource's image file from.
   *
   * If no image is set, or the image is not found, the resource's initials are shown instead.
   *
   * By default, it is inherited from the owning Calendar:
   * ```javascript
   * new Calendar({
   *     resourceImagePath   : 'images/resources/'
   *     modes : {
   *         dayresource : {
   *             showHeaderAvatars : true,
   *         }
   *     }
   * });
   * ```
   * @config {Boolean|'last'}
   */
  showHeaderAvatars: true,
  autoRefresh: [
    "hideEmptyResources",
    "resourceDateFilter"
  ],
  /**
   * Configure this as `true` to hide resource columns which contain no events.
   *
   * <div class="note">Use with care. This may result in no resource columns being rendered
   * for completely empty dates.</div>
   * @prp {Boolean}
   * @default false
   */
  hideEmptyResources: null,
  /**
   * A callback function which an application may provide to filter out resource columns for
   * particular dates.
   * @prp {Function|String} resourceDateFilter A function, or the name of a function in the ownership hierarchy.
   * @param {DayCell} context Information about the day column to be created.
   * @returns {Boolean} Return `false` to not display a column for the passed resource at the passed date.
   */
  resourceDateFilter: null,
  /**
   * By default, when a resource leaf column drops to below 100 pixels wide, the resource avatar
   * hides.
   *
   * Configure this as `true` to hide resource __name__&nbsp;when the resource leaf column width
   * drops below 100 pixels wide.
   * @prp {Boolean}
   * @default false
   */
  hideResourceNameWhenNarrow: null
});
DayResourceView.initClass();
DayResourceView._$name = "DayResourceView";

// lib/Calendar/view/mixin/EventNavigation.js
var isCalendarMixin = (w) => w.isCalendarMixin || w.isSchedulerBase;
var EventNavigation_default2 = (Target) => class EventNavigation extends EventNavigation_default(Target || Base) {
  // region Hidden configs
  /**
   * @hidefunctions beforeAssignmentDelete
   */
  // endRegion
  static get configurable() {
    return {
      /**
       * A config object to use when creating the {@link Core.helper.util.Navigator}
       * to use to perform keyboard navigation in the timeline.
       * @config {NavigatorConfig}
       * @default
       * @category Misc
       * @internal
       */
      navigator: {
        allowCtrlKey: true,
        scrollSilently: true,
        datasetIdProperty: "eventId",
        // TAB navigation is used, so the Navigator *reacts* to navigation rather than
        // listens for keystrokes and moves focus.
        itemsTabbable: true,
        // Override inherited SchedulerEventNavigation handlers
        keys: {
          ArrowUp: null,
          ArrowDown: null,
          Escape: null,
          Tab: null,
          "SHIFT+Tab": null
        }
      }
    };
  }
  normalizeTarget(event) {
    return event.eventRecord;
  }
  processEvent(keyEvent) {
    const { target } = keyEvent;
    if (keyEvent.key === "Enter") {
      if (target.closest(".b-sch-event-wrap")) {
        return;
      }
      if (target.role === "link" && target.closest(".b-overflowpopup")) {
        DomHelper.triggerMouseEvent(target, "click");
        return keyEvent;
      }
    }
    return super.processEvent(...arguments);
  }
  changeNavigator(navigator2) {
    const me = this;
    me.getConfig("eventCls");
    return super.changeNavigator(me.constructor.mergeConfigs({
      target: me.element,
      itemSelector: `.b-cal-cell-overflow,.b-cal-cell-no-content,${me.eventSelector}:not(.b-minimal),.b-sch-event-wrap`,
      // Calendar doesn't have a scrollable.
      skipScrollIntoView: true
    }, navigator2));
  }
  onViewRefresh() {
    var _a5, _b2;
    const { activeItem } = this.navigator;
    (_a5 = super.onViewRefresh) == null ? void 0 : _a5.call(this, ...arguments);
    if (activeItem && !((_b2 = this.activeView._overflowPopup) == null ? void 0 : _b2.containsFocus)) {
      const activeEvent = this.getEventRecord(activeItem);
      this.navigator.updateActiveItem(activeEvent ? this.getElementFromEventRecord(activeEvent) : null, activeItem);
    }
  }
  onCalendarStoreChange({ action, records }) {
    var _a5, _b2;
    const me = this, { navigator: navigator2 } = me, activeItem = navigator2 == null ? void 0 : navigator2.activeItem, activeItemPos = (_a5 = Rectangle.from(activeItem)) == null ? void 0 : _a5.center, { usingKeyboard } = DomHelper;
    if (activeItem && "eventId" in activeItem.dataset && !me.eventStore.includes(activeItem.dataset.eventId)) {
      const activeView = me.constructor.fromElement(activeItem, isCalendarMixin) || me.activeView, {
        _overflowPopup,
        isSchedulerBase
      } = activeView, navigator3 = isSchedulerBase ? activeView.navigator : me.navigator, nextItem = navigator3.next, enclosingCell = DomHelper.findParent(activeItem, DomHelper.isFocusable);
      if (isSchedulerBase) {
        const enclosingCell2 = activeView.getCellElementFromDomEvent({ target: activeItem, clientY: activeItemPos.y });
        return (nextItem || navigator3.previous || enclosingCell2).focus({ preventScroll: !usingKeyboard });
      }
      if (nextItem === activeItem.nextSibling && !nextItem.parentNode.syncIdMap) {
        navigator3.triggerNavigate({
          eventPhase: Event.BUBBLING_PHASE,
          target: nextItem
        }, nextItem);
      } else {
        let fallback = nextItem || navigator3.previous;
        if (!fallback || !enclosingCell.contains(fallback)) {
          fallback = enclosingCell;
        }
        if ((_overflowPopup == null ? void 0 : _overflowPopup.containsFocus) && !_overflowPopup.contentElement.contains(fallback)) {
          const { target } = _overflowPopup.lastAlignSpec;
          fallback = navigator3.getAdjacent(1, target.nextSibling) || navigator3.getAdjacent(-1, target);
        }
        if (!fallback || !me.element.contains(fallback)) {
          me.widgetMap.viewContainer.revertFocus(true);
        } else {
          const nextActiveDate = isSchedulerBase ? activeView.getDateFromXY([activeItemPos.x, activeItemPos.y], null, false) : activeView.getDateFromElement(fallback), nextActiveEventId = fallback.dataset.eventId, nextActiveView = me.constructor.fromElement(fallback).closest(isCalendarMixin);
          fallback.focus({ preventScroll: !usingKeyboard });
          nextActiveView.ion({
            refresh: () => {
              var _a6;
              if (nextActiveEventId) {
                fallback = nextActiveView.getEventElement(nextActiveEventId, nextActiveDate);
                if (fallback) {
                  if (fallback === DomHelper.getActiveElement(fallback)) {
                    navigator3.triggerNavigate({
                      eventPhase: Event.BUBBLING_PHASE,
                      target: fallback
                    }, fallback);
                  } else {
                    fallback.focus({ preventScroll: !usingKeyboard });
                  }
                }
                navigator3.updateActiveItem(fallback, activeItem);
              } else if (nextActiveView.isCalendarRow && ((_a6 = nextActiveView.parent) == null ? void 0 : _a6.isDayView) && !nextActiveView.cellMap.size) {
                nextActiveView.requestAnimationFrame(() => nextActiveView.parent.focusDate(fallback.dataset.date));
              }
            },
            once: true
          });
        }
      }
    }
    (_b2 = super.onCalendarStoreChange) == null ? void 0 : _b2.call(this, ...arguments);
  }
  onElementKeyDown(keyEvent) {
    var _a5;
    if (keyEvent.target.closest(this.navigator.itemSelector)) {
      (_a5 = super.onElementKeyDown) == null ? void 0 : _a5.call(this, keyEvent);
    }
  }
  /**
   * The currently focused event record.
   * @property {Scheduler.model.EventModel}
   * @readonly
   */
  get activeEvent() {
    const { activeItem } = this.navigator;
    if (activeItem) {
      return this.resolveEventRecord(activeItem);
    }
  }
  // This does not need a className on Widgets.
  // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
  // to the Widget it's mixed in to should implement this.
  get widgetClass() {
  }
};

// lib/Calendar/view/mixin/EventSelection.js
var EventSelection_default2 = (Target) => {
  var _a5;
  return _a5 = class extends EventSelection_default(Target || Base) {
    //region Events
    /**
     * Fired any time there is a change to the events selected in the Calendar.
     * @event selectionChange
     * @param {Calendar.view.Calendar} source The Calendar instance.
     * @param {'select'|'deselect'|'update'|'clear'} action One of the actions 'select', 'deselect', 'update',
     * 'clear'
     * @param {Scheduler.model.EventModel[]} selected An array of the Events added to the selection.
     * @param {Scheduler.model.EventModel[]} deselected An array of the Event removed from the selection.
     * @param {Scheduler.model.EventModel[]} selection The new selection.
     */
    //endregion
    normalizeTarget(event) {
      return event.eventRecord;
    }
    updateDeselectAllOnScheduleClick(deselectAllOnScheduleClick) {
      this[deselectAllOnScheduleClick ? "ion" : "un"]({
        scheduleClick: "clearEventSelection"
      });
    }
    /**
     * Responds to mutations of the underlying selection Collection.
     * Keeps the UI synced, selectionChange event is fired when `me.silent` is falsy.
     * @private
     */
    onSelectedCollectionChange({ added, removed }) {
      const me = this, selection = me.selectedCollection, selected = added || [], deselected = removed || [];
      me.updateSelected();
      if (!me.silent) {
        const action = selection.count > 0 ? selected.length > 0 && deselected.length > 0 ? "update" : selected.length > 0 ? "select" : "deselect" : "clear";
        me.trigger("selectionChange", {
          action,
          selection: selection.values,
          selected,
          deselected
        });
      }
    }
    onCalendarStoreChange({ action, records }) {
      var _a6;
      if (action === "remove") {
        this.selectedCollection.remove(...records);
      }
      (_a6 = super.onCalendarStoreChange) == null ? void 0 : _a6.call(this, ...arguments);
    }
    /**
     * Deselects all {@link Scheduler.model.EventModel events}.
     * @category Selection
     */
    clearEventSelection() {
      this.selectedCollection.clear();
    }
    /**
     * The {@link Scheduler.model.EventModel events} which are selected.
     * @property {Scheduler.model.EventModel[]}
     * @category Selection
     */
    get selectedEvents() {
      return this.selectedCollection.values;
    }
    set selectedEvents(events) {
      events = ArrayHelper.asArray(events);
      this.selectedCollection.splice(0, this.selectedCollection.count, events);
    }
    /**
     * Returns `true` if the {@link Scheduler.model.EventModel event} is selected.
     * @param {Scheduler.model.EventModel} event The event
     * @returns {Boolean} Returns `true` if the event is selected
     * @category Selection
     */
    isEventSelected(event) {
      return this.selectedCollection.includes(event);
    }
    /**
     * Selects the passed {@link Scheduler.model.EventModel event} *if it is not selected*.
     * @param {Scheduler.model.EventModel} event The event to select
     * @param {Boolean} [preserveSelection] Pass `true` to preserve any other selected events
     * @category Selection
     */
    select(event, preserveSelection = false) {
      this.selectEvent(event, preserveSelection);
    }
    /**
     * Selects the passed {@link Scheduler.model.EventModel event} *if it is not selected*.
     * @param {Scheduler.model.EventModel} event The event select
     * @param {Boolean} [preserveSelection] Pass `true` to preserve any other selected events
     * @category Selection
     */
    selectEvent(event, preserveSelection = false) {
      const { selectedCollection } = this;
      if (!this.isEventSelected(event) && this.isEventSelectable(event) !== false) {
        preserveSelection ? selectedCollection.add(event) : selectedCollection.splice(0, selectedCollection.count, event);
      }
    }
    /**
     * Deselects the passed {@link Scheduler.model.EventModel event}*if it is selected*.
     * @param {Scheduler.model.EventModel} event The event to deselect.
     * @category Selection
     */
    deselect(event) {
      this.deselectEvent(event);
    }
    /**
     * Deselects the passed {@link Scheduler.model.EventModel event} *if it is selected*.
     * @param {Scheduler.model.EventModel} event The event to deselect.
     * @category Selection
     */
    deselectEvent(event) {
      if (this.isEventSelected(event)) {
        this.selectedCollection.remove(event);
      }
    }
    /**
     * Adds {@link Scheduler.model.EventModel events} to the selection.
     * @param {Scheduler.model.EventModel[]} events Events to be selected
     * @category Selection
     */
    selectEvents(events) {
      this.selectedCollection.add(events.filter((event) => this.isEventSelectable(event) !== false));
    }
    /**
     * Removes {@link Scheduler.model.EventModel events} from the selection.
     * @param {Scheduler.model.EventModel[]} events Events to be deselected
     * @category Selection
     */
    deselectEvents(events) {
      this.selectedCollection.remove(events);
    }
    /**
     * Mouse listener to update selection.
     * @private
     */
    onEventSelectionClick(clickedRecord) {
      if (this.isEventSelected(clickedRecord)) {
        if (GlobalEvents_default.ctrlKeyDown) {
          this.deselectEvent(clickedRecord);
        }
      } else {
        this.selectEvent(clickedRecord, this.multiEventSelect && GlobalEvents_default.ctrlKeyDown);
      }
    }
    /**
     * Navigation listener to update selection.
     * @private
     */
    onEventNavigate({ event, item }) {
      if (item) {
        const eventRecord = this.activeView.getEventRecord(item);
        if (eventRecord) {
          if (event.button === 2 && this.isEventSelected(eventRecord)) {
            return;
          }
          this.onEventSelectionClick(eventRecord);
        } else if (this.deselectAllOnScheduleClick) {
          this.clearEventSelection();
        }
      }
    }
    // we do not need this class to participate in widget CSS classes
    get widgetClass() {
    }
  }, //region Hidden configs
  /**
   * @hideconfigs eventAssignHighlightCls
   */
  /**
   * @hideevents eventSelectionChange, assignmentSelectionChange
   */
  /**
   * @hideproperties selectedAssignments
   */
  /**
   * @hidefunctions deselectAssignment, deselectAssignments, isAssignmentSelected, selectAssignment, selectAssignments
   */
  //endregion
  __publicField(_a5, "configurable", {
    /**
     * Configure as `false` to preserve selection when clicking the empty schedule area.
     * @prp {Boolean}
     * @default
     * @category Selection
     */
    deselectAllOnScheduleClick: true,
    eventSelectedCls: "b-selected"
  }), _a5;
};

// lib/Calendar/widget/ModeSelector.js
var menuItemListeners = {
  hide() {
    this.$overflowClone.hide();
  },
  show() {
    this.$overflowClone.show();
  }
};
var ModeSelector = class extends Container.mixin(Fencible_default, Minifiable_default, Responsive_default) {
  get childItems() {
    const result = super.childItems, { _button } = this;
    if (_button) {
      result.push(_button);
    }
    return result;
  }
  get hideNonWorkingDays() {
    var _a5;
    return (_a5 = this.calendar) == null ? void 0 : _a5.hideNonWorkingDays;
  }
  set hideNonWorkingDays(value2) {
    const { calendar } = this;
    if (calendar && !(!value2 && calendar.hideNonWorkingDays == null)) {
      calendar.hideNonWorkingDays = value2;
    }
  }
  configure(config = {}) {
    if (this.isConstructing && "minified" in config && !("minifiable" in config)) {
      config = ObjectHelper.assign({
        minifiable: false
      }, config);
    }
    super.configure(config);
  }
  changeIncludeWeekendsButton(config, existing) {
    var _a5;
    const me = this, includeWeekendsButton = config && ((_a5 = me.calendar) == null ? void 0 : _a5.includeWeekendsButton);
    if (includeWeekendsButton) {
      config = Button.mergeConfigs(config, includeWeekendsButton);
    }
    if (config && !existing) {
      return me.add(config);
    }
    return Button.reconfigure(
      existing,
      config,
      /* owner = */
      me
    );
  }
  updateIncludeWeekendsButton(includeWeekendsButton) {
    if (includeWeekendsButton) {
      includeWeekendsButton.toggleGroup = null;
    }
    this.syncFullWeek();
  }
  changeIncludeWeekendsMenuItem(config, existing) {
    var _a5;
    const menu = (_a5 = this == null ? void 0 : this.button) == null ? void 0 : _a5.menu;
    if (config && !existing) {
      return menu == null ? void 0 : menu.add(config);
    }
    return MenuItem.reconfigure(
      existing,
      config,
      /* owner = */
      menu
    );
  }
  updateIncludeWeekendsMenuItem() {
    this.syncFullWeek();
  }
  onBeforeFirstShowModeMenu({ menu }) {
    const me = this, { calendar } = me, { activeView } = calendar, modes = Object.values(calendar.modes);
    let menuItem, mode;
    for (mode of modes) {
      menuItem = mode.$modeMenuItem;
      menuItem.hidden = mode.$modeSelectorItem.hidden;
      menu.add(menuItem);
      menuItem.checked = mode === activeView;
      menuItem.onToggle = "up.onToggleMenuItem";
    }
    me.getConfig("includeWeekendsMenuItem");
    me.syncFullWeek();
    me.menuReady = true;
  }
  addMode(name, view, options) {
    const me = this, { buttonBar, calendar } = me, text = view[me.titleProperty] || view.displayName || view.type, key = view.activationKey || text.charAt(0).toLowerCase(), keyIndex = text.toLowerCase().indexOf(key), menuItem = view.$modeMenuItem = MenuItem.reconfigure(view.$modeMenuItem, MenuItem.mergeConfigs({
      type: MenuItem,
      view,
      // Button carries a reference to the view it shows
      ref: `${name}ShowMenuItem`,
      checked: false,
      closeParent: true,
      toggleGroup: `${me.id}-modeSelectorMenuItem`,
      localizable: false,
      text: [
        { text },
        { text: key.toUpperCase(), class: "b-menuitem-key" }
      ],
      weight: 0
    }, options, view.selectorMenuItem)), button = view.$modeSelectorItem = menuItem.$overflowClone = buttonBar.add(Button.mergeConfigs({
      view,
      // Button carries a reference to the view it shows
      ref: `${name}ShowButton`,
      toggleGroup: `${me.id}-modeSelectorButton`,
      text: keyIndex < 0 ? [
        text,
        { text: key.toUpperCase(), class: "b-button-key b-align-end" }
      ] : [
        keyIndex ? text.substring(0, keyIndex) : null,
        { text: key[keyIndex ? "toLowerCase" : "toUpperCase"](), class: "b-button-key" },
        text.substring(keyIndex + 1)
      ],
      // Needs ariaLabel because the text is chopped up into multiple elements and this reads wrong
      ariaLabel: text,
      localizable: false,
      onBeforeToggle: "up.onBeforeToggleButton",
      onToggle: "up.onToggleButton",
      weight: 0
    }, options, view.selectorButton));
    view.$activateMode = (e) => {
      var _a5;
      return calendar.setConfig({
        date: ((_a5 = e.target.closest("[data-date]")) == null ? void 0 : _a5.dataset.date) || calendar.date,
        mode: view
      });
    };
    menuItem.ion(menuItemListeners);
    (me.ownedWidgets || (me.ownedWidgets = {}))[menuItem.id] = menuItem;
    calendar.addKeyBinding(key, view.$activateMode);
    calendar.addDescendant(menuItem);
    me.getConfig("includeWeekendsButton");
    view.ion({
      thisObj: me,
      localized() {
        const text2 = view[me.titleProperty] || view.displayName || view.type, newKey = (view.activationKey || text2.charAt(0)).toLowerCase(), keyIndex2 = text2.toLowerCase().indexOf(newKey);
        if (newKey !== key) {
          calendar.removeKeyBinding(key, view.$activateMode);
          calendar.addKeyBinding(newKey, view.$activateMode);
        }
        button.text = keyIndex2 < 0 ? [
          { text: text2 },
          { text: newKey.toUpperCase(), class: "b-button-key" }
        ] : [
          keyIndex2 ? { text: text2.substring(0, keyIndex2) } : null,
          { text: newKey.toUpperCase(), class: "b-button-key" },
          { text: text2.substring(keyIndex2 + 1) }
        ];
        menuItem.text = [
          { text: text2 },
          { text: newKey.toUpperCase(), class: "b-menuitem-key" }
        ];
      }
    });
  }
  onBeforeToggleButton({ pressed }) {
    if (pressed && this.calendar.viewContainer.layout.isChangingCard) {
      return false;
    }
  }
  onToggleButton({ source, pressed }) {
    if (pressed) {
      this.calendar.mode = source.view;
    }
  }
  updateDisabled(disabled, was) {
    super.updateDisabled(disabled, was);
    this.buttonBar.items.forEach((button) => {
      if (!disabled || !button.containsFocus) {
        button.disabled = disabled;
      }
    });
  }
  onToggleMenuItem({ source, checked }) {
    if (checked) {
      this.calendar.mode = source.view;
    }
  }
  onToggleWorkingDaysButton({ pressed }) {
    this.hideNonWorkingDays = !pressed;
  }
  onToggleWorkingDaysMenu({ checked }) {
    this.hideNonWorkingDays = !checked;
  }
  syncActiveMode(activeView) {
    var _a5;
    const me = this, buttons = me.buttonBar.items;
    let item;
    me.button.text = activeView.displayName;
    me.includeWeekendsButton.hidden = !activeView.hasNonWorkingDays;
    me.syncFullWeek();
    for (item of buttons) {
      if (item.view) {
        item.pressed = item.view === activeView;
      }
    }
    if (me.menuReady) {
      for (item of me.button.menu.items) {
        if (((_a5 = item.view) == null ? void 0 : _a5.$modeMenuItem) === item) {
          item.checked = item.view === activeView;
        }
      }
    }
    me[buttons.length > 1 ? "show" : "hide"]();
  }
  syncFullWeek() {
    const {
      calendar,
      hideNonWorkingDays,
      _includeWeekendsButton: includeWeekendsButton,
      _includeWeekendsMenuItem: includeWeekendsMenuItem
    } = this, fullWeek = !hideNonWorkingDays;
    if (!calendar.isConfiguring) {
      if (includeWeekendsButton) {
        includeWeekendsButton.pressed = fullWeek;
      }
      if (includeWeekendsMenuItem) {
        includeWeekendsMenuItem.checked = fullWeek;
      }
    }
  }
  changeButton(config, existing) {
    return Button.reconfigure(existing, config);
  }
  updateButton(button) {
    button && this.add(button);
  }
  changeButtonBar(config, existing) {
    return ButtonGroup.reconfigure(existing, config);
  }
  updateButtonBar(buttonBar) {
    buttonBar && this.add(buttonBar);
  }
  changeCalendar(calendar) {
    if (calendar && !calendar.isWidget) {
      calendar = this.up(calendar);
    }
    return calendar;
  }
  updateCalendar(calendar) {
    this.detachListeners("cal");
    this.syncFullWeek();
    calendar == null ? void 0 : calendar.ion({
      name: "cal",
      changeHideNonWorkingDays: "syncFullWeek",
      thisObj: this
    });
  }
};
//region Config
__publicField(ModeSelector, "$name", "ModeSelector");
__publicField(ModeSelector, "type", "calendarmodeselector");
__publicField(ModeSelector, "configurable", {
  // Since Minifiable is @internal for now
  /**
   * Set to `false` to prevent this widget from assuming is {@link #config-minified} form automatically (for
   * example, due to {@link Core.widget.Toolbar#config-overflow} handling.
   *
   * When this value is `true` (the default), the minifiable widget's {@link #config-minified} config may be
   * set to `true` to reduce toolbar overflow.
   *
   * @config {Boolean}
   * @default
   */
  minifiable: true,
  /**
   * The config property that will be used as the readable title for each mode.
   * @config {String}
   * @default
   */
  titleProperty: "title",
  /**
   * Set to `true` to present this widget in its minimal form.
   * @config {Boolean}
   * @default false
   */
  minified: null,
  /**
   * The button or button config object that will display a popup menu to select the calendar mode.
   * @config {Core.widget.Button}
   */
  button: {
    ref: "modeSelectorButton",
    type: "button",
    icon: "b-icon-calendar-days",
    // avoid b-icon-menu since toolbar overflow also uses that icon
    cls: "b-calendar-mode-button",
    menu: [],
    internalListeners: {
      beforeShowMenu: "up.onBeforeFirstShowModeMenu",
      once: true
    }
  },
  /**
   * The button group or button group config object that will display the calendar modes, one per button.
   * @config {Core.widget.ButtonGroup}
   */
  buttonBar: {
    type: "buttongroup",
    ignoreParentReadOnly: true
  },
  calendar: {
    value: "calendar",
    $config: "lazy"
  },
  /**
   * The {@link Core.widget.Button} that will toggle the display of non-working days.
   *
   * This property will only exist if the Calendar's {@link Calendar.view.Calendar#config-includeWeekendsButton} has been
   * set to `true`
   *
   * If that property is set to `false`, this property will still yield a button instance, but it will be hidden by CSS rules.
   * @property {Core.widget.Button}
   * @readonly
   */
  includeWeekendsButton: {
    $config: "lazy",
    value: {
      ref: "weekendsButton",
      type: "button",
      cls: "b-calendar-fullweek-button",
      toggleGroup: `no`,
      // so ButtonGroup does not take over
      text: "L{ModeSelector.weekends}",
      tooltip: "L{ModeSelector.includeWeekends}",
      onToggle: "up.onToggleWorkingDaysButton",
      weight: -1
    }
  },
  /**
   * The {@link Core.widget.MenuItem} that will toggle the display of non-working days when the Calendar UI is narrow
   * and the modes are selected using a menu.
   *
   * This property will only exist if the Calendar's {@link Calendar.view.Calendar#config-includeWeekendsButton} has been
   * set to `true` and the Calendar is in Responsive `small` mode.
   * @property {Core.widget.Button}
   * @readonly
   */
  includeWeekendsMenuItem: {
    $config: "lazy",
    value: {
      ref: "weekendsMenuItem",
      separator: true,
      text: "L{ModeSelector.includeWeekends}",
      checked: false,
      onToggle: "up.onToggleWorkingDaysMenu",
      weight: 10
    }
  },
  layout: {
    type: "box",
    wrap: false
  },
  overflowable: "none",
  responsive: {
    large: {
      // Allow toolbar overflow to kick us into minified state
      minifiable: true,
      minified: null
    },
    "*": {
      // Don't allow toolbar overflow to kick us out of minified state
      minifiable: false,
      minified: true
    }
  },
  responsiveTarget: "@calendar"
});
__publicField(ModeSelector, "fenced", {
  onToggleButton: true,
  onToggleMenuItem: true,
  syncFullWeek: true,
  syncActiveMode: {
    all: "syncActiveMode",
    lock: ["onToggleButton", "onToggleMenuItem", "syncActiveMode"]
  }
});
ModeSelector.initClass();
ModeSelector._$name = "ModeSelector";

// lib/Calendar/feature/EventCopyPaste.js
var EventCopyPaste2 = class extends EventCopyPaste {
  // There's no merging of pluginConfigs
  static get pluginConfig() {
    const config = {};
    for (const prop in super.pluginConfig) {
      config[prop] = [...super.pluginConfig[prop]];
    }
    config.assign.push("beforeListRenderRow");
    return config;
  }
  constructor(config) {
    super(config.client, config);
    this.client.ion({
      cellOverFlowClick: "onScheduleClick",
      thisObj: this
    });
    EventHelper.on({
      element: this.client.element,
      focusmove: "onFocusMove",
      thisObj: this
    });
  }
  // When we navigate to a new cell, we need to update the cellClickedContext
  onFocusMove(e) {
    const me = this, { client } = me, { _target } = e, date2 = client.getDateFromDomEvent(_target);
    if (date2) {
      const eventRecord = client.getEventRecord(_target.closest("[class*=-event-wrap]"));
      if (eventRecord) {
        me.targetTime = eventRecord.startDate;
      } else if (me.targetTime) {
        DateHelper.copyTimeValues(date2, me.targetTime);
      }
      me._cellClickedContext = {
        date: date2,
        domEvent: e,
        source: client.activeView,
        view: client.activeView,
        resourceRecord: me.client.resolveResourceRecord(_target)
      };
    } else {
      me._cellClickedContext - null;
    }
  }
  populateEventMenu(event) {
    const { client } = this;
    this.fixEventData(event);
    super.populateEventMenu(event);
    if (client.activeView.isEventList && !client.readOnly && this.hasClipboardData() !== false) {
      event.items.pasteEvent = {
        text: "L{pasteEvent}",
        localeClass: this,
        icon: "b-icon b-icon-paste",
        disabled: client.resourceStore.count === 0 || event.resourceRecord.readOnly,
        weight: 110,
        onItem: ({ eventRecord }) => {
          this.pasteEvents(eventRecord.startDate);
        }
      };
    }
  }
  onScheduleMenuPasteClick({ date: date2, resourceRecord, domEvent, feature }) {
    const { activeView, constructor } = this.client, contextView = constructor.fromElement(feature.menuContext.targetElement, (w) => w.isCalendarMixin);
    if (!activeView.isResourceView && !activeView.isDayResourceView) {
      resourceRecord = null;
    }
    if ((contextView == null ? void 0 : contextView.parent) && contextView.parent.allDayEvents === contextView) {
      this._isPastingIntoAllDay = true;
    }
    this.pasteEvents(date2, resourceRecord);
  }
  onEventDataGenerated(renderData) {
    this.fixEventData(renderData);
    super.onEventDataGenerated(renderData);
  }
  fixEventData(renderData) {
    var _a5;
    if (!renderData.assignmentRecord) {
      const { eventRecord, resourceRecord } = renderData;
      renderData.assignmentRecord = (_a5 = resourceRecord && eventRecord.assignments.find((a) => a.eventId === eventRecord.id && a.resourceId === resourceRecord.id)) != null ? _a5 : eventRecord;
    }
  }
  // Overridden to always provide events so to be able to copy all the event assignments
  copyEvents(records, isCut) {
    if (records == null ? void 0 : records[0].isAssignmentModel) {
      records = records.map((r) => r.event);
    }
    return super.copyEvents(records, isCut);
  }
  pasteEvents(date2, resourceRecord) {
    const { _cellClickedContext } = this, { isResourceView, isDayResourceView } = this.client.activeView;
    if (date2) {
      this.targetTime = date2;
    }
    if (!date2 && !resourceRecord && _cellClickedContext && (isResourceView || isDayResourceView)) {
      return super.pasteEvents(_cellClickedContext.date, _cellClickedContext.resourceRecord);
    }
    return super.pasteEvents(...arguments);
  }
  cloneData({ data, isPaste }) {
    var _a5, _b2;
    const cloned = super.cloneData({ data, isPaste });
    cloned.assignmentRecords = data.assignmentRecords.map((a) => {
      const copy = a.copy(), originalEvent = data.eventRecords.find((r) => r.id === copy.eventId);
      copy.event = cloned.eventRecords.find((r) => r.originalInternalId === originalEvent.internalId);
      return copy;
    });
    (_a5 = cloned.eventRecords) == null ? void 0 : _a5.forEach((e) => delete e.meta.originalResourceId);
    if (isPaste && this._isPastingIntoAllDay) {
      (_b2 = cloned.eventRecords) == null ? void 0 : _b2.forEach((e) => e.allDay = true);
      this._isPastingIntoAllDay = false;
    }
    return cloned;
  }
  refreshClient() {
    var _a5;
    const { activeView } = this.client;
    this.client.refresh();
    if ((_a5 = activeView._overflowPopup) == null ? void 0 : _a5.isVisible) {
      activeView._overflowPopup.refresh();
    }
    if (activeView.isResourceView) {
      activeView.eachView((view) => view.refresh());
    }
  }
  beforeListRenderRow({ row, record }) {
    row.cls["b-cut-item"] = record.assignments.some((assignment) => assignment.meta.isCut);
  }
};
__publicField(EventCopyPaste2, "$name", "EventCopyPaste");
__publicField(EventCopyPaste2, "type", "eventCopyPaste");
/**
 * @hideconfigs copyPasteAction
 */
__publicField(EventCopyPaste2, "configurable", {
  allowNoAssignments: true
});
CalendarFeature.register(EventCopyPaste2.type, EventCopyPaste2);
EventCopyPaste2._$name = "EventCopyPaste";

// lib/Calendar/widget/EventEditor.js
var EventEditor2 = class extends EventEditor {
  //endregion
  updateRecord(record) {
    this.fixTimeFieldVisibility(record.allDay);
    super.updateRecord(record);
  }
  onAllDayChange({ value: value2, userAction }) {
    this.fixTimeFieldVisibility(value2, userAction);
  }
  fixTimeFieldVisibility(allDay, fromUI) {
    const hideShowMethod = allDay ? "hide" : "show", {
      startDateField,
      startTimeField,
      endDateField,
      endTimeField
    } = this.widgetMap;
    startTimeField == null ? void 0 : startTimeField[hideShowMethod]();
    endTimeField == null ? void 0 : endTimeField[hideShowMethod]();
    if (!allDay && fromUI) {
      startDateField && (startDateField.value = startTimeField.value = this.record.getData("startDate"));
      endDateField && (endDateField.value = endTimeField.value = this.record.getData("endDate"));
    }
  }
  onFocusOut() {
    super.onFocusOut(...arguments);
    this.owner = this.initialConfig.owner;
  }
};
//region Config
__publicField(EventEditor2, "$name", "EventEditor");
// Factoryable type name
__publicField(EventEditor2, "type", "calendareventeditor");
__publicField(EventEditor2, "configurable", {
  align: {
    monitorResize: true
    // Needed because DayView events change size
  }
});
EventEditor2.initClass();
EventEditor2._$name = "EventEditor";

// lib/Calendar/feature/EventEdit.js
var isCalendar = (w) => w.isCalendar;
var EventEdit2 = class extends EventEdit {
  construct(config) {
    super.construct(config.client, config);
  }
  async onDragCreateEnd({ eventRecord, resourceRecord }) {
    eventRecord._dragCreated = true;
    await this.editNewlyCreatedEvent(eventRecord, resourceRecord);
    eventRecord._dragCreated = null;
  }
  onEventAutoCreated({ eventRecord }) {
    this.editNewlyCreatedEvent(eventRecord);
  }
  editNewlyCreatedEvent(eventRecord, resourceRecord) {
    if (!this.disabled) {
      eventRecord.isCreating = true;
      return this.editEvent(eventRecord, resourceRecord, null, false);
    }
  }
  /**
   * Opens an editor for the passed event. This function is exposed on Calendar and can be called as
   * `calendar.editEvent()`.
   *
   * If the event is not present in the `eventStore`, the event will be added so that it becomes
   * visible in the UI. The editor will then shows a "Cancel" button which removes the event when
   * clicked to abort a "New Event" operation.
   * @method editEvent
   * @param {Scheduler.model.EventModel} eventRecord Event to edit
   * @param {Scheduler.model.ResourceModel} [resourceRecord] Not used. Inherited from Scheduler.
   * @param {HTMLElement} [element] Element to anchor editor to (defaults to events element)
   * @async
   * @on-owner
   */
  // editEvent is the single entry point in the base class.
  // Subclass implementations of the action may differ, so are implemented in doEditEvent
  async doEditEvent(eventRecord, resourceRecord, element) {
    var _a5, _b2, _c2, _d, _e, _f;
    const me = this, { client } = me, { eventStore } = client, { startDate, endDate } = eventRecord;
    let view = client.eventSource || client.activeSubView;
    if (eventRecord.isChronoModelMixin) {
      const chronoWidget = bryntum.query((w) => (w.isGantt || w.isSchedulerPro) && w.project === client.project);
      if (chronoWidget) {
        await eventStore.addAsync(eventRecord);
        view.doRefresh();
        return chronoWidget.features.taskEdit.editTask(eventRecord, element || view.getEventElement(eventRecord));
      }
    }
    if (view.isSchedulerBase) {
      me.client = view;
      await super.doEditEvent(...arguments);
      me.client = client;
      return;
    }
    if (!view.isCalendarMixin || ((_b2 = (_a5 = view.features) == null ? void 0 : _a5.cellEdit) == null ? void 0 : _b2.isEditing)) {
      return;
    }
    if (eventRecord.startDate && eventRecord.endDate && !DateHelper.intersectSpans(
      startDate,
      endDate,
      view.startDate,
      view.endDate
    )) {
      client.date = eventRecord.startDate;
    }
    if (view.allDayEvents && view.isAllDayEvent(eventRecord) && view.showAllDayHeader) {
      view = view.allDayEvents;
    }
    if (!eventRecord.isOccurrence) {
      let added;
      if (!eventStore.includes(eventRecord)) {
        eventStore.add(eventRecord);
        added = true;
      }
      if (added || eventRecord._dragCreated) {
        await view.afterRefresh({ forceRefresh: true });
      }
    }
    const { _overflowPopup } = view, activeDate = view.getDateFromElement(element);
    let fromOverflow = _overflowPopup == null ? void 0 : _overflowPopup.isVisible, eventElement = fromOverflow ? _overflowPopup.getEventElement(eventRecord) : view.getEventElement(eventRecord, activeDate);
    if (fromOverflow) {
      if (eventElement) {
        fromOverflow = false;
      } else {
        eventElement = _overflowPopup.getEventElement(eventRecord);
      }
    }
    if (eventRecord.isCreating) {
      (_c2 = client.onEventCreated) == null ? void 0 : _c2.call(client, eventRecord);
    }
    if (!eventElement && view.isEventList) {
      eventElement = (_e = (_d = view == null ? void 0 : view.rowManager) == null ? void 0 : _d.getRowFor(eventRecord)) == null ? void 0 : _e.element;
    }
    if (fromOverflow) {
      eventElement.focus();
    } else if (eventElement) {
      const visibleRect = DomHelper.isInView(eventElement), needsScroll = !visibleRect || visibleRect.height < 100 && eventElement.offsetHeight > Math.ceil(visibleRect.height) || visibleRect.width < eventElement.getBoundingClientRect().width;
      if (needsScroll) {
        await Scroller.scrollIntoView(eventElement, {
          animate: true,
          focus: true,
          maxHeight: 100
        });
      } else {
        eventElement.focus({ preventScroll: true });
      }
    } else {
      if (!view.isAllDayEvent(eventRecord) && (!startDate || view.hiddenNonWorkingDays[startDate.getDay()])) {
        me.internalShowEditor(eventRecord, null, {
          target: true
        });
        return;
      }
      if (startDate && (!eventRecord.isCreating || !eventRecord._dragCreated)) {
        await view.scrollTo(eventRecord);
      }
      eventElement = view.getEventElement(eventRecord);
    }
    if (!eventElement && !element && view.isDayCellRenderer) {
      eventElement = view.getCell(eventRecord.startDate);
    }
    if (me.isDestroyed) {
      return;
    }
    me.internalShowEditor(eventRecord, null, {
      target: eventElement || element,
      allowTargetOut: view.isAnimating
    });
    if ((_f = me.editor) == null ? void 0 : _f.lastAlignSpec) {
      me.editor.lastAlignSpec.allowTargetOut = false;
    }
  }
  async onSaveClick() {
    var _a5;
    const me = this, { eventRecord } = me, { isCreating } = eventRecord.meta;
    if (me.isValid) {
      eventRecord.isCreating = false;
    }
    const saved = await super.onSaveClick(...arguments);
    if (isCreating && saved) {
      me.eventStore.added.add(eventRecord);
      if (!me.eventStore.usesSingleAssignment) {
        me.assignmentStore.added.add(eventRecord.assignments);
      }
      (_a5 = me.client.crudManager) == null ? void 0 : _a5.onCrudStoreChange();
    }
  }
  internalShowEditor() {
    var _a5, _b2, _c2, _d;
    const {
      client,
      editor,
      startTimeField,
      endTimeField
    } = this, calendar = client.closest(isCalendar) || client, autoCreate = (_b2 = (_a5 = calendar.activeView) == null ? void 0 : _a5.autoCreate) != null ? _b2 : calendar.autoCreate || client.constructor.configurable.autoCreate, step = autoCreate == null ? void 0 : autoCreate.step, { activeSubView } = calendar, _overflowPopup = activeSubView == null ? void 0 : activeSubView._overflowPopup;
    (_d = (_c2 = (client.isSchedulerBase ? client.calendar : client).features.eventTooltip) == null ? void 0 : _c2._tooltip) == null ? void 0 : _d.hide();
    editor.owner = (_overflowPopup == null ? void 0 : _overflowPopup.isVisible) ? _overflowPopup : activeSubView;
    if (super.internalShowEditor(...arguments) === false) {
      editor.owner = editor.initialConfig.owner;
      return;
    }
    if (step) {
      if (startTimeField && !startTimeField.initialConfig.step) {
        startTimeField.step = step;
      }
      if (endTimeField && !endTimeField.initialConfig.step) {
        endTimeField.step = step;
      }
    }
  }
  // calendar drag/auto create is different enough from scheduler's, so we just override this method
  // to empty function to opt-out of the STM capture mechanism
  async finalizeStmCapture(saved) {
  }
};
__publicField(EventEdit2, "$name", "EventEdit");
__publicField(EventEdit2, "type", "eventEdit");
__publicField(EventEdit2, "configurable", {
  editorConfig: {
    type: "calendareventeditor",
    items: {
      resourceField: {
        label: "L{Calendar}",
        showEventColor: true
      },
      allDay: {
        type: "checkbox",
        cls: "b-match-label",
        name: "allDay",
        weight: 250,
        label: "L{All day}",
        internalListeners: {
          change: "up.onAllDayChange"
        }
      }
    }
  }
});
CalendarFeature.register(EventEdit2.type, EventEdit2);
EventEdit2._$name = "EventEdit";

// lib/Calendar/feature/EventMenu.js
var hasEventStore3 = (w) => w.eventStore;
var EventMenu2 = class extends EventMenu {
  // Superclass registers this as a chained function.
  populateEventMenu({ items: items2, eventRecord, assignmentRecord }) {
    super.populateEventMenu(...arguments);
    const { client } = this;
    items2.duplicate = {
      disabled: eventRecord.readOnly || (assignmentRecord == null ? void 0 : assignmentRecord.readOnly),
      hidden: client.readOnly
    };
  }
  getDataFromEvent(event) {
    var _a5;
    const { client } = this, { activeView } = client, { eventTooltip } = client.features;
    if (this.triggerEvent === "contextmenu" && (eventTooltip == null ? void 0 : eventTooltip.showOn) === "contextmenu" && eventTooltip.enabled) {
      event.preventDefault();
    } else {
      if (activeView.isSchedulerBase) {
        this.client = activeView;
      } else {
        (_a5 = event.target.closest(".b-cal-event-wrap")) == null ? void 0 : _a5.focus();
      }
      const result = super.getDataFromEvent(event);
      this.client = client;
      return result;
    }
  }
  changeMenu() {
    const me = this, result = super.changeMenu(...arguments);
    result == null ? void 0 : result.ion({
      beforeShow({ source }) {
        const { activeSubView } = me.client, { _overflowPopup } = activeSubView;
        source.owner = (_overflowPopup == null ? void 0 : _overflowPopup.containsFocus) ? _overflowPopup : activeSubView;
      }
    });
    return result;
  }
  changeItems(items2) {
    items2 = super.changeItems(...arguments);
    items2.unassignEvent = null;
    return items2;
  }
};
__publicField(EventMenu2, "$name", "EventMenu");
__publicField(EventMenu2, "type", "eventMenu");
__publicField(EventMenu2, "configurable", {
  // ContextMenuBase was changed to allow Menus to remain in place while target scrolls away.
  // (https://github.com/bryntum/bryntum-suite/pull/9226)
  menu: {
    scrollAction: "realign"
  },
  /**
   * This is a preconfigured set of items used to create the default context menu. The default options are
   * listed at the top of the page.
   *
   * To remove existing items, set corresponding keys `null`:
   *
   * ```javascript
   * const calendar = new Calendar({
   *     features : {
   *         eventMenu : {
   *             items : {
   *                 deleteEvent : null,
   *                 duplicate : null
   *             }
   *         }
   *     }
   * });
   * ```
   *
   * See the feature config in the above example for more details.
   *
   * @config {Object<String,MenuItemConfig|Boolean|null>} items
   */
  items: {
    duplicate: {
      text: "L{EventMenu.duplicateEvent}",
      icon: "b-icon b-icon-copy",
      weight: 400,
      onItem({ eventRecord }) {
        const dupe = eventRecord.copy(null, { skipFieldIdentifiers: true }), { eventStore } = this.up(hasEventStore3), { resources } = eventRecord;
        dupe.name = `${eventRecord.name} (${this.L("L{EventMenu.copy}")})`;
        dupe.recurrence = null;
        if (resources == null ? void 0 : resources.length) {
          eventStore.assignmentStore.assignEventToResource(dupe, resources);
        }
        eventStore.add(dupe);
      }
    }
  },
  /**
   * An event which is used to show event menu. The available values for it are
   * `contextmenu`, `click` and `dblclick`. If no value is specified,
   * default value is used from {@link Calendar/view/Calendar#config-contextMenuTriggerEvent}
   *
   * ```javascript
   * const calendar = new Calendar({
   *      features : {
   *          eventMenu : {
   *              triggerEvent : 'click'
   *          }
   *      }
   * });
   * ```
   *
   * @config {'contextmenu'|'click'|'dblclick'|null}
   */
  triggerEvent: null
});
CalendarFeature.register(EventMenu2.type, EventMenu2);
EventMenu2._$name = "EventMenu";

// lib/Calendar/feature/EventTooltip.js
var hasEventStore4 = (w) => w.eventStore;
var ownConfigs = [
  "activeElement",
  "client",
  "clientListeners",
  "disabled",
  "showOn",
  "tooltip"
];
var EventTooltip2 = class extends CalendarFeature {
  static get pluginConfig() {
    return {
      chain: ["render"]
    };
  }
  // Because we insert ourself into the owner hierarchy, isVisible consults us
  get isVisible() {
    return true;
  }
  changeRecurrenceConfirmation(recurrenceConfirmation, existingInstance) {
    if (recurrenceConfirmation) {
      recurrenceConfirmation.rootElement = this.owner.rootElement;
    }
    return Widget.reconfigure(existingInstance, recurrenceConfirmation, {
      owner: this
    });
  }
  changeTooltip(config, existingInstance) {
    const me = this, cfg = me.config;
    if (config) {
      config = EventTip.mergeConfigs({
        ownerFeature: me,
        forElement: me.client.element,
        id: `${me.client.id}-event-tip`,
        // The Tooltip must activate on embedded widgets from other product families
        forSelector: me.showOn === "hover" && ".b-cal-event-wrap,.b-sch-event-wrap,.b-gantt-task-wrap,.b-taskboard-card",
        resources: me.resources,
        disabled: me.disabled,
        internalListeners: {
          thisObj: me,
          delete: "onDeleteClick",
          edit: "onEditClick",
          overTarget: "onTipPointerOver"
        }
      }, config);
      Object.keys(cfg).forEach((k) => {
        if (!ownConfigs[k] && (me.hasConfig(k) || k in me.initialConfig)) {
          config[k] = cfg[k];
        }
      });
    }
    return EventTip.reconfigure(existingInstance, config, me);
  }
  render() {
    const me = this, clientListeners = {
      eventsPerCellChange: "onClientEventsPerCellChange",
      navigate: "onClientNavigate",
      renderEvents: "onRenderEvents",
      thisObj: me
    };
    if (me.showOn === "hover") {
      me.getConfig("tooltip");
    } else {
      if (me.showOn === "contextmenu") {
        me.alignToDomEvent = true;
      }
      clientListeners[`event${me.showOn}`] = clientListeners[`task${me.showOn}`] = "onClientTooltipGesture";
    }
    me.client.ion(clientListeners);
  }
  get owner() {
    var _a5;
    if ((_a5 = this.client.activeView._overflowPopup) == null ? void 0 : _a5.containsFocus) {
      return this.client.activeView.overflowPopup;
    } else {
      return this.client;
    }
  }
  //region Events
  addListener(...args) {
    var _a5;
    (_a5 = this.tooltip) == null ? void 0 : _a5.addListener(...args);
  }
  removeListener(...args) {
    var _a5;
    (_a5 = this.tooltip) == null ? void 0 : _a5.removeListener(...args);
  }
  onEditClick() {
    var _a5, _b2;
    const { client, activeClient, tooltip } = this, { overflowPopup } = activeClient, eventEdit = ((_a5 = activeClient == null ? void 0 : activeClient.features) == null ? void 0 : _a5.eventEdit) || client.features.eventEdit, fromPopup = (_b2 = overflowPopup == null ? void 0 : overflowPopup.element) == null ? void 0 : _b2.contains(tooltip.activeTarget), target = fromPopup ? overflowPopup.targetCell : tooltip.activeTarget;
    if (eventEdit && !eventEdit.disabled) {
      eventEdit.editEvent(this.eventRecord, null, target, false);
      tooltip.hide();
    }
  }
  onDeleteClick() {
    this.activeClient.calendar.removeEvents([this.eventRecord], () => {
      var _a5;
      return (_a5 = this.tooltip) == null ? void 0 : _a5.hide();
    }, this.tooltip);
  }
  onClientEventsPerCellChange() {
    var _a5, _b2;
    (_b2 = this._tooltip) == null ? void 0 : _b2.requestAnimationFrame((_a5 = this._tooltip) == null ? void 0 : _a5.hide);
  }
  onClientNavigate(navEvent) {
    var _a5;
    const { _tooltip } = this;
    if ((_tooltip == null ? void 0 : _tooltip.activeTarget) && (!this.owner.owns(navEvent.event) || navEvent.item && navEvent.item !== _tooltip.activeTarget) && !_tooltip.owns((_a5 = navEvent.event) == null ? void 0 : _a5.relatedTarget)) {
      _tooltip.hide();
    }
  }
  onRenderEvents(e) {
    const { _tooltip } = this;
    if ((_tooltip == null ? void 0 : _tooltip.activeTarget) && !this.activeClient.element.contains(this.tooltip.activeTarget)) {
      _tooltip.hide();
    }
  }
  onClientTooltipGesture({ domEvent, event, eventElement, eventRecord, source: owningCalendarWidget }) {
    const { tooltip } = this;
    domEvent = domEvent || event;
    if (this.onTipPointerOver({ event: domEvent, newTarget: eventElement, eventRecord, owningCalendarWidget }) !== false) {
      if (eventElement.classList.contains("b-cal-in-cluster") && (this.alignToDomEvent || parseAlign(tooltip.align.align).edgeAligned === 2)) {
        tooltip.setTimeout({
          fn: "showByEvent",
          args: [domEvent, eventElement],
          delay: 130
        });
      } else {
        tooltip.showByEvent(domEvent, eventElement.closest("[data-event-id],[data-task-id]"));
      }
    }
  }
  onTipPointerOver({
    event,
    newTarget,
    owningCalendarWidget,
    eventRecord
  }) {
    var _a5, _b2, _c2, _d, _e;
    const { tooltip, client } = this;
    if (!owningCalendarWidget) {
      owningCalendarWidget = (_a5 = Widget.fromElement(newTarget)) == null ? void 0 : _a5.closest(hasEventStore4);
    }
    if (!eventRecord) {
      eventRecord = owningCalendarWidget.getEventRecord(newTarget);
    }
    if (((_c2 = (_b2 = client.features) == null ? void 0 : _b2.eventEdit) == null ? void 0 : _c2.isEditing) || ((_e = (_d = client.activeView.features) == null ? void 0 : _d.cellEdit) == null ? void 0 : _e.isEditing)) {
      return false;
    }
    if (client.floatRoot.querySelector(".b-eventeditor,.b-taskeditor")) {
      return false;
    }
    if (owningCalendarWidget) {
      if (event) {
        tooltip.triggeredByEvent = event;
        tooltip.activeTarget = newTarget;
      }
      this.activeClient = owningCalendarWidget;
      eventRecord = owningCalendarWidget.getEventRecord(newTarget);
      tooltip.eventRecord = this.eventRecord = eventRecord;
    }
    return eventRecord != null;
  }
  //endregion
  //region Internal
  updateDisabled(disabled, was) {
    super.updateDisabled(disabled, was);
    if (this._tooltip) {
      this._tooltip.disabled = disabled;
    }
  }
  //endregion
};
__publicField(EventTooltip2, "$name", "EventTooltip");
__publicField(EventTooltip2, "type", "eventTooltip");
__publicField(EventTooltip2, "configurable", {
  /**
   * The gesture which activates the event tooltip. Defaults to `'click'`, but may be set to
   * `'contextmenu'` or `'mouseover`'. The tip persists until closed.
   *
   * If set to `'hover'`, the tip shows on mouseover and hides on mouseout.
   *
   * If set to `'contextmenu'`, the {@link Calendar.feature.EventMenu} is not shown while this is enabled
   * and the tip shows at the mouse/touch contact point, otherwise it aligns to the target event bar.
   * @config {'click'|'contextmenu'|'mouseover'|'hover'}
   * @default
   */
  showOn: "click",
  closable: true,
  // Allow it to fall back from its initial alignment axis to the cross axis.
  // So if aligned l-r or r-l and shown by a long multiday event, it allows
  // itself to be fall back to being aligned t-b
  axisLock: "flexible",
  /**
   * Gets the Tooltip instance that this feature is using.
   * @member {Calendar.widget.EventTip} tooltip
   * @readonly
   */
  /**
   * This config is used to directly configure the associated {@link Calendar.widget.EventTip tooltip}.
   * @config {EventTipConfig}
   */
  tooltip: {
    $config: ["lazy", "nullify"],
    value: {
      type: "eventTip"
    }
  },
  /**
   * A function, or the *name* of a function which, when passed the active
   * {@link Scheduler.model.EventModel}, returns a value to use as the tooltip's
   * {@link Core.widget.Panel#config-title}.
   *
   * The function may return either an HTML string, or a {@link DomConfig} object.
   * _Defaults to using the event `name`_
   *
   * @config {Function|String}
   * @param {Scheduler.model.EventModel} record Event record
   * @returns {String|DomConfig}
   */
  titleRenderer: (eventRecord) => StringHelper.encodeHtml(eventRecord.name),
  /**
   * A function, or the *name* of a function called to update the tooltip's content when the
   * cursor is moved over a target.
   *
   * It receives one argument containing context about the tooltip and show operation.
   * The function may return either an HTML string, or a {@link DomConfig} object, or
   * a Promise yielding one of these.
   *
   * ```javascript
   * new Calendar({
   *     features : {
   *         eventTooltip : {
   *             renderer : 'up.getEventTip'
   *         }
   *     },
   *     getEventTip({ eventRecord }) {
   *         return {
   *             className : 'tooltip-content',
   *             text      : eventRecord.name
   *         }
   *     }
   * });
   * ```
   *
   * or
   *
   * ```javascript
   * new Calendar({
   *     features : {
   *         eventTooltip : {
   *             renderer : 'up.getEventTip'
   *         }
   *     },
   *     getEventTip({ eventRecord }) {
   *         return '<div class="tooltip-content> + eventRecord.name + '</div>';
   *     }
   * });
   * ```
   *
   * or
   *
   * ```javascript
   * new Calendar({
   *     features : {
   *         eventTooltip : {
   *             renderer : 'up.getEventTip'
   *         }
   *     },
   *     getEventTip : async function({ eventRecord }) {
   *         // Use a web service which returns a JSON DomConfig block
   *         const response = await fetch(`getEventTipContent?event=${eventRecord.id}`);
   *         return response.json();
   *     }
   * });
   * ```
   *
   * @config {Function|String}
   * @param {Object} context
   * @param {Scheduler.model.EventModel} context.eventRecord The event record which the tooltip is being shown for.
   * @param {Core.widget.Tooltip} context.tip The tooltip instance
   * @param {HTMLElement} context.element The Element for which the Tooltip is monitoring mouse movement
   * @param {HTMLElement} context.activeTarget The target element that triggered the show
   * @param {Event} context.event The raw DOM event
   * @returns {String|Promise|DomConfig}
   */
  renderer: null,
  /**
   * This config is used to directly configure the associated recurrence confirmation popup used
   * when a delete is requested.
   * @config {RecurrenceConfirmationPopupConfig}
   * @private
   */
  recurrenceConfirmation: {
    $config: ["lazy", "nullify"],
    value: {
      type: "recurrenceconfirmation"
    }
  },
  /**
   * By default, the end date of an all day event is displayed in the tooltip UI as
   * the last calendar date on which the event falls. For most end users, this is the
   * expected value.
   *
   * Technically, the {@link Scheduler.model.EventModel#field-endDate} is a timestamp
   * which represents the exact point in time at which an event ends. To use this instead,
   * configure `extendAllDayEndDay` as `true`.
   *
   * To be clear, this would mean that an {@link Scheduler.model.EventModel#field-allDay}
   * event starting and ending on the 7th of February 2020, would show the end date in the
   * tooltip as 8th of February 2020.
   * @config {Boolean}
   * @default false
   */
  extendAllDayEndDay: null,
  /**
   * Defines how to align the EventTooltip to its event.
   *
   * The value can be either a simple string or a full configuration object.
   *
   * When using a simple string, the format is `'[trblc]n-[trblc]n'` and it specifies tooltip edge and
   * the event edge plus optional offsets from 0 to 100 along the edges to align to. Also supports direction
   * independent edges horizontally, `s` for start and `e` for end (maps to `l` and `r` for LTR, `r` and `l`
   * for RTL).
   *
   * For more details about using the object form, see {@link Core.widget.Widget#function-showBy}.
   *
   * Once set, this is stored internally in object form.
   * @config {AlignSpec|String} align
   * @default 't-b'
   */
  align: null,
  /**
   * By default, if the tip's target event is in a cluster of overlapping events and therefore
   * narrow, activating the tip will expand it to full width temporarily.
   *
   * Configure this as `false` to disable this.
   * @prp {Boolean}
   * @default true
   */
  revealEventsInCluster: true,
  resources: null,
  /**
   * If using `{@link #config-showOn} : 'click'` (the default), this is the amount of time to delay
   * showing after the click gesture. This may be used if you do not wish the tooltip to appear on the
   * first click of a double click gesture. By default there is no delay, and the tip is shown instantly.
   * @prp {Number}
   * @default
   */
  clickDelay: null,
  /**
   * By default, the tooltip aligns to the event bar according to the {@link #config-align} setting.
   *
   * If set to `true`, the tooltip will show at the DOM event point which triggered the tooltip.
   *
   * This is automatically set to `true` when using `{@link #config-showOn} : 'contextmenu'`.
   * @prp {Boolean}
   * @default false
   */
  alignToDomEvent: null
});
EventTooltip2.initClass();
EventTooltip2._$name = "EventTooltip";

// lib/Calendar/feature/ScheduleTooltip.js
var isCalendarMixin2 = (w) => w.isCalendarMixin;
var ScheduleTooltip2 = class extends CalendarFeature {
  static get pluginConfig() {
    return {
      chain: ["render"]
    };
  }
  changeTooltip(config, existingInstance) {
    if (config) {
      const me = this, { client } = me, tip = Tooltip.reconfigure(existingInstance, ObjectHelper.assign({}, config, {
        id: `${client.id}-schedule-tip`,
        owner: client,
        forElement: client.viewContainer.element,
        getHtml: me.getHtml.bind(me),
        filterTarget: me.filterTarget.bind(me),
        internalListeners: {
          innerHtmlUpdate({ source }) {
            me.clockTemplate.updateDateIndicator(source.element, me.lastTime);
          }
        }
      }));
      me.clockTemplate = new ClockTemplate({
        scheduler: client,
        mode: "hour"
      });
      return tip;
    }
  }
  filterTarget({ target }) {
    var _a5;
    if (!GlobalEvents_default.isMouseDown() && this.callback(this.showScheduleTip, this, [(_a5 = Widget.fromElement(target)) == null ? void 0 : _a5.closest(isCalendarMixin2)])) {
      return target.closest(".b-cal-event-wrap") ? null : target;
    }
  }
  getHtml({ tip, event, activeTarget }) {
    var _a5, _b2;
    const me = this, activeView = (_a5 = Widget.fromElement(activeTarget)) == null ? void 0 : _a5.closest(isCalendarMixin2), date2 = me.callback(me.showScheduleTip, me, [activeView]) && activeView.getDateFromDomEvent(event);
    if (date2 && !(event.type === "pointermove" && event.pointerType === "touch" && Object.values(((_b2 = me.client.features.drag) == null ? void 0 : _b2.zones) || {}).some((z) => z.dragging))) {
      const { isDayView } = activeView, text = DateHelper.format(date2, `${activeView.dateFormat}${isDayView ? " " + (activeView.timeFormat || "LST") : ""}`);
      me.lastTime = date2;
      return isDayView ? me.clockTemplate.generateContent({ date: date2, text }) : text;
    }
    tip.hide();
  }
  onCalendarScroll() {
    const { tooltip } = this;
    if (tooltip.isVisible) {
      const { triggeredByEvent } = tooltip, targetElement = DomHelper.elementFromPoint(triggeredByEvent.clientX, triggeredByEvent.clientY);
      targetElement.dispatchEvent(new BrowserHelper.PointerEventConstructor("pointermove", triggeredByEvent));
    }
  }
  render() {
    this.getConfig("tooltip");
    EventHelper.on({
      element: this.client.element,
      scroll: "onCalendarScroll",
      thisObj: this,
      capture: true
    });
  }
  //region Internal
  updateDisabled(disabled, was) {
    super.updateDisabled(disabled, was);
    if (this._tooltip) {
      this._tooltip.disabled = disabled;
    }
  }
  //endregion
};
__publicField(ScheduleTooltip2, "$name", "ScheduleTooltip");
__publicField(ScheduleTooltip2, "type", "ScheduleTooltip");
__publicField(ScheduleTooltip2, "configurable", {
  /**
   * A function (or name of a function in the ownership hierarchy) which decides whether to show the ScheduleTooltip
   * depending on the active view. The active view is passed, and if a truthy value is returned, the tip is shown,
   * otherwise it is hidden.
   *
   * *This defaults to only showing for {@link Calendar.widget.DayView}s*
   * @prp {Function} showScheduleTip
   * @param {Calendar.widget.mixin.CalendarMixin} view The Calendar view that the pointer is over. Return `true` to
   * show the ScheduleTooltip for the passed view.
   * @returns {Boolean} `true` to show the Schedule tooltip for the passed view.
   * @accepts {Function|String}
   */
  showScheduleTip: (v) => {
    var _a5;
    return (v == null ? void 0 : v.isDayView) || ((_a5 = v == null ? void 0 : v.viewType) == null ? void 0 : _a5.isDayView);
  },
  /**
   * Gets the Tooltip instance that this feature is using.
   * @member {Core.widget.Tooltip} tooltip
   * @readonly
   */
  /**
   * This config is used to directly configure the associated {@link Core.widget.Tooltip}.
   * @config {TooltipConfig}
   */
  tooltip: {
    $config: ["lazy", "nullify"],
    value: {
      type: "tooltip",
      allowOver: true,
      hoverDelay: 0,
      hideDelay: 100,
      forSelector: "*",
      showOnHover: true,
      anchorToTarget: false,
      trackMouse: true,
      updateContentOnMouseMove: true,
      // disable text content and monitor resize for tooltip, otherwise it doesn't
      // get sized properly on first appearance
      monitorResize: false,
      textContent: false,
      layoutStyle: "white-space:nowrap"
    }
  }
});
CalendarFeature.register(ScheduleTooltip2.type, ScheduleTooltip2, true);
ScheduleTooltip2._$name = "ScheduleTooltip";

// lib/Calendar/feature/ScheduleMenu.js
var isCalendarMixin3 = (w) => w.isCalendarMixin;
var ScheduleMenu2 = class extends ScheduleMenu {
  getDataFromEvent(event) {
    const { client } = this, { activeView } = client, resourceRecord = client.resolveResourceRecord(event) || client.defaultCalendar;
    if (activeView.isSchedulerBase) {
      this.client = activeView;
    }
    const result = super.getDataFromEvent(event);
    if (!result.resourceRecord) {
      result.resourceRecord = resourceRecord;
    }
    this.client = client;
    return result;
  }
  shouldShowMenu({ date: date2, targetElement }) {
    const { client } = this, { activeView } = client;
    if (activeView.isEventList && !activeView.isAgendaView) {
      return false;
    }
    if (activeView.isSchedulerBase) {
      this.client = activeView;
      const result = super.shouldShowMenu(...arguments);
      this.client = client;
      return result;
    }
    return Boolean(date2 && !targetElement.closest(client.eventSelector));
  }
  populateScheduleMenu({ items: items2, domEvent, date: date2 }) {
    super.populateScheduleMenu(...arguments);
    const { client } = this, { addEvent } = items2, view = client.constructor.fromElement(this.menuContext.targetElement, isCalendarMixin3);
    if (addEvent) {
      addEvent.disabled = this.disableAddEvent;
      addEvent.onItem = () => {
        const resourceRecord = client.activeView.getResourceRecord(domEvent);
        if (client.activeView.trigger("beforeAutoCreate", { view, domEvent, date: date2, resourceRecord }) !== false) {
          (view || client).createEvent(date2, resourceRecord, true);
        }
      };
    }
  }
  get disableAddEvent() {
    const { resourceRecord, date: date2 } = this.menuContext;
    return resourceRecord ? resourceRecord.readOnly || !resourceRecord.isWorkingTime(date2) : false;
  }
  changeMenu() {
    const me = this, result = super.changeMenu(...arguments);
    result == null ? void 0 : result.ion({
      beforeShow({ source }) {
        const { activeSubView } = me.client, { _overflowPopup } = activeSubView;
        source.owner = (_overflowPopup == null ? void 0 : _overflowPopup.containsFocus) ? _overflowPopup : activeSubView;
      }
    });
    return result;
  }
};
__publicField(ScheduleMenu2, "$name", "ScheduleMenu");
__publicField(ScheduleMenu2, "type", "scheduleMenu");
__publicField(ScheduleMenu2, "configurable", {
  // ContextMenuBase was changed to allow Menus to remain in place while target scrolls away.
  // (https://github.com/bryntum/bryntum-suite/pull/9226)
  menu: {
    scrollAction: "realign"
  },
  /**
   * An event which is used to show schedule menu. The available values for it are
   * `contextmenu`, `click` and `dblclick`. If no value is specified, default
   * value is used from {@link Calendar/view/Calendar#config-contextMenuTriggerEvent}
   *
   * ```javascript
   * const calendar = new Calendar({
   *      features : {
   *          scheduleMenu : {
   *              triggerEvent : 'click'
   *          }
   *      }
   * });
   * ```
   *
   * @config {'contextmenu'|'click'|'dblclick'|null}
   */
  triggerEvent: null
});
CalendarFeature.register(ScheduleMenu2.type, ScheduleMenu2, true);
ScheduleMenu2._$name = "ScheduleMenu";

// lib/Calendar/view/Calendar.js
var relayedEvents = /^refresh$|.*event.*|.*resource.*|.*cell.*|.*row.*|.*schedule.*|daynumber.*|weeknumber.*|monthname.*|weekname.*$|beforedatechange|tickchange|.*showoverflowpopup|beforeautocreate|rangechange|weekflex|layoutupdate/;
var isFocusedCalendarMixin2 = (w) => w.isCalendarMixin && w.containsFocus;
var emptyArray16 = Object.freeze([]);
var hasWeight = (m) => m.weight != null;
var noDateChangeEvents = {
  weeknumberclick: 1,
  monthnameclick: 1
};
var isMonthView = (v) => {
  var _a5;
  return v.isMonthView || ((_a5 = v.view) == null ? void 0 : _a5.type) === "monthview";
};
var isWeekView = (v) => {
  var _a5;
  return v.isWeekView || ((_a5 = v.view) == null ? void 0 : _a5.type) === "weekview";
};
var isNavigable = (e) => {
  var _a5;
  return ((_a5 = e.getAttribute) == null ? void 0 : _a5.call(e, "role")) !== "presentation" && DomHelper.isFocusable(e);
};
var isConfiguredAway = (configObject, prop) => ObjectHelper.hasOwn(configObject, prop) && (configObject[prop] === null || configObject[prop] === false);
var inheritConfigs = (config, source, props) => {
  for (let i = 0, { length } = props; i < length; i++) {
    const prop = props[i];
    if (!(prop in config) && source[prop] !== void 0) {
      config[prop] = source[prop];
    }
  }
};
var _Calendar = class _Calendar extends Panel.mixin(
  SchedulerInterface_default,
  CalendarStores_default,
  Featureable_default,
  CrudManagerView_default,
  EventNavigation_default2,
  EventSelection_default2,
  CurrentConfig_default,
  Responsive_default,
  State_default,
  RecurringEvents_default
) {
  static get configurable() {
    return {
      // region Hidden configs
      /**
       * @hideevents eventSelectionChange
       */
      /**
       * @hideconfigs htmlCls, autoUpdateRecord, record, textContent, content, html
       */
      /**
       * @hideproperties content, html
       */
      // endRegion
      // Insert features config here because it is inherited from Featurable, which is @internal
      // and therefore does not show up in the API docs in its default state.
      /**
       * Specifies the features to create and associate with the Calendar.
       *
       * The keys of this object are the names of features. The values are config objects for those features.
       *
       * ```javascript
       *     features : {
       *         // Exclude the CalendarDrag feature from the Calendar
       *         drag : false,
       *
       *         // Change the default configuration of a feature
       *         eventTooltip : {
       *             showOn : 'hover'
       *         }
       *     }
       * ```
       * @config {Object} features
       */
      /**
       *
       * After construction, this property can be used to access the Calendar's features and
       * reconfigure them.
       *
       * For example:
       *
       * ```javascript
       *  myCalendar.features.eventTooltip.disabled = true;
       * ```
       * @member {Object} features
       */
      layout: "hbox",
      /**
       * Enables events being recurring and also adds extra recurrence UI fields in the built-in event editor.
       *
       * If you want to disable the recurrence popup, you can set the `defaultAction` to `future` to
       * affect all future occurrences, or `single` to just affect the currently selected event.
       * @config {Boolean|Object}
       * @property {'single'|'future'} [defaultAction] Enables avoiding the recurrence confirmation
       * popup user interface by automatically updating either the single occurrence, or the occurrence
       * and all future occurrences.
       * @default true
       * @category Scheduled events
       */
      enableRecurringEvents: true,
      localizableProperties: [
        "autoCreate.newName"
      ],
      crudManager: null,
      features: {
        drag: {},
        eventCopyPaste: {},
        eventEdit: {},
        eventTooltip: {},
        scheduleMenu: {},
        eventMenu: {}
      },
      /**
       * Indicates where the Next/Previous/Today buttons should be placed.
       * @config {'toolbar'|'sidebar'}
       * @default 'toolbar'
       */
      navigatorPlacement: null,
      eventContainerSelector: ".b-eventrenderer,.b-overflowpopup",
      responsive: {
        small: {
          when: 600,
          once: {
            mode: "day"
          },
          dateFormat: "MMM DD, YYYY"
        },
        medium: {
          when: 800
        },
        large: {
          overlaySidebar: false
        },
        "*": {
          overlaySidebar: true,
          once: {
            mode: "week"
          }
        }
      },
      responsiveRoot: true,
      stateful: [
        "hideNonWorkingDays",
        "mode"
      ],
      /**
       * A {@link Core.widget.Toolbar} which displays and manages the calendar title
       * and the buttons which manipulate the Calendar's temporal navigation and
       * active mode.
       *
       * Provided widgets include:
       *
       * - `toggleSidebar` A button to collapse and expand the {@link #config-sidebar}
       * - `todayButton` A button which moves the active view to include today's date.
       * - `prevButton` A button which moves the active view to its previous time span.
       * <br>__Note:__ The active view must yield a {@link Calendar.widget.mixin.CalendarMixin#property-stepUnit}
       * to use as the tooltip hint for this button, otherwise it will be disabled.
       * - `nextButton` A button which moves the active view to its next time span.
       * <br>__Note:__ The active view must yield a {@link Calendar.widget.mixin.CalendarMixin#property-stepUnit}
       * to use as the tooltip hint for this button, otherwise it will be disabled.
       * - `viewDescription` A widget which is used to display the `description` property of the active view
       *  (e.g. "October 2025" in month view)
       * - `spacer` A spacer widget which pushes following items to be aligned to the right.
       * - `modeSelector` A {@link Calendar.widget.ModeSelector} which contains buttons for selecting which
       *   view is active. This is hidden if only one {@link #config-modes mode} is enabled in the Calendar.
       *
       * These have `weight` values 100 to 700. New items can be inserted at any position by
       * configuring them with an appropriate `weight`.
       *
       * @member {Core.widget.Toolbar} tbar
       * @readonly
       * @category Content
       */
      /**
       * A config object containing definitions of the toolbar of the Calendar.
       *
       * Its `items` property defines the buttons and informational widgets
       * provided by default.
       *
       * There are several provided widgets, each of which may be reconfigured using
       * an object, or disabled by configuring them as `null`.
       *
       * ```javascript
       * tbar : {
       *     items : {
       *         prevButton    : null,
       *         toggleSidebar : null
       *     }
       * }
       * ```
       *
       * Provided widgets include:
       *
       * - `toggleSidebar` A button to collapse and expand the {@link #config-sidebar}
       * - `todayButton` A button which moves the active view to include today's date.
       * - `prevButton` A button which moves the active view to its previous time span.
       * <br>__Note:__ The active view must yield a {@link Calendar.widget.mixin.CalendarMixin#property-stepUnit}
       * to use as the tooltip hint for this button, otherwise it will be disabled.
       * - `nextButton` A button which moves the active view to its next time span.
       * <br>__Note:__ The active view must yield a {@link Calendar.widget.mixin.CalendarMixin#property-stepUnit}
       * to use as the tooltip hint for this button, otherwise it will be disabled.
       * - `viewDescription` A widget which is used to display the `description` property of the active view.
       * - `spacer` A spacer widget which pushes following items to be aligned to the right.
       * - `modeSelector` A {@link Calendar.widget.ModeSelector} which contains buttons for selecting which
       *   view is active. This is hidden if only one {@link #config-modes mode} is enabled in the Calendar.
       *
       * These have `weight` values 100 to 800. New items can be inserted at any position by
       * configuring them with an appropriate `weight`.
       *
       * Configure this as `null` to remove the toolbar entirely.
       * @config {Array<ContainerItemConfig|String>|ToolbarConfig|CalendarToolbarConfig|null}
       * @typings {Array<ContainerItemConfig|String>|ToolbarConfig|CalendarToolbarConfig|PagingToolbarConfig|null}
       */
      tbar: {
        cls: "b-calendar-toolbar",
        items: {
          toggleSidebar: {
            icon: "b-icon-menu-vertical",
            cls: "b-sidebar-toggle b-borderless b-transparent",
            onClick: "up.onToggleSidebarClick",
            tooltip: "L{Calendar.toggleSidebar}",
            weight: 100
          },
          todayButton: {
            text: "L{Calendar.Today}",
            cls: "b-cal-nav-item b-calendar-today-button",
            icon: "b-icon-calendar-day",
            onClick: "up.shiftToNow",
            weight: 200
          },
          prevButton: {
            onClick: "up.shiftPrevious",
            cls: "b-cal-nav-item b-borderless b-transparent",
            icon: "b-icon-previous",
            weight: 300
          },
          nextButton: {
            onClick: "up.shiftNext",
            cls: "b-cal-nav-item b-borderless b-transparent b-cal-nav-next",
            icon: "b-icon-next",
            weight: 400
          },
          viewDescription: {
            cls: "b-calendar-view-desc",
            type: "widget",
            flex: "",
            html: "\xA0",
            overflowable: "none",
            weight: 500
          },
          spacer: {
            type: "widget",
            cls: "b-toolbar-fill",
            weight: 600
          },
          modeSelector: {
            type: "calendarModeSelector",
            weight: 700
          }
        }
      },
      /**
       * The child items of the Calendar view.
       *
       * The Calendar contains two child items:
       *
       * - `sidebar` The sidebar which by default contains a clickable date picker to show and/or select
       * the active date, and a Calendar filter UI to filter events belonging to the calendars
       * defined in the {@link Calendar.mixin.CalendarStores#config-resourceStore}.
       * - `viewContainer` A {@link Core.widget.layout.Card card layout}
       * {@link Core.widget.Container container} which contains the enabled {@link #config-modes}.
       *
       * These items may be reconfigured by specifying them as an object, for example:
       *
       * ```javascript
       * new Calendar({
       *     appendTo : document.body,
       *
       *     crudManager : {
       *         transport : {
       *             load : {
       *                 url : '/data-providing-url'
       *             }
       *             sync : {
       *                 url : '/data-update-url'
       *             }
       *         },
       *         autoLoad : true,
       *         autoSync : true
       *     },
       *
       *     // Reconfigure our viewContainer to change which "mode" is active upon startup
       *     items : {
       *         viewContainer : {
       *             // Start looking at the week - its the fourth "mode" by default.
       *             layout : {
       *                 activeIndex : 3
       *             }
       *         }
       *     }
       * });
       * ```
       * @config {Object}
       * @private
       */
      items: {},
      /**
       * Configuration options to change how the subordinate {@link Core.widget.Container} which contains
       * the calendar's child views is created.
       *
       * After instantiation, this Container may be referenced using the {@link #property-viewContainer} property
       *
       * For example:
       * ```javascript
       * new Calendar({
       *     appendTo : document.body,
       *
       *     crudManager : {
       *         loadUrl  : '/data-providing-url',
       *         syncUrl  : '/data-update-url',
       *         autoLoad : true,
       *         autoSync : true
       *     },
       *
       *     // Reconfigure our viewContainer to change which "mode" is active upon startup
       *     viewContainer : {
       *         // No sliding in or out of child views
       *         layout : {
       *             animateCardChange : false
       *         }
       *     }
       * });
       * ```
       * @config {ContainerConfig} viewContainer
       */
      viewContainer: {
        $config: "lazy",
        value: {
          type: "container",
          role: "presentation",
          weight: 100,
          cls: {
            "b-calendar-viewcontainer": 1
          },
          flex: "1 1 100%",
          layout: {
            type: "card"
          },
          scrollable: {
            overflowY: true,
            overflowX: true
          },
          suppressChildHeaders: true,
          layoutStyle: {
            padding: 0
          },
          internalListeners: {
            beforeActiveItemChange: "up.onBeforeViewContainerCardChange",
            activeItemChange: "up.onViewContainerCardChange"
          }
        }
      },
      /**
       * Set to `true` to show the "Weekends" toggle button of the mode selector. Alternatively, this config
       * can be a {@link Core.widget.Button} config object to configure the button.
       *
       * See {@link Calendar.widget.ModeSelector#property-includeWeekendsButton} and
       * {@link Calendar.widget.ModeSelector#property-includeWeekendsMenuItem} for more information on
       * accessing the resulting widget(s).
       * @config {Boolean|ButtonConfig}
       * @default
       */
      includeWeekendsButton: false,
      layoutStyle: {
        padding: 0,
        flexFlow: "row nowrap"
      },
      /**
       * The {@link #config-modes} as widget instances.
       * @member {Object<String,CalendarView>} modes
       */
      /**
       * Configures the view modes which are to be available in this Calendar.
       *
       * Normally, the property names in this object are the `type` names of the eight standard
       * Calendar views, and so the corresponding configuration object should be an object which
       * configures that type:
       *
       * - agenda - Configuration for a {@link Calendar.widget.AgendaView}
       * - year - Configuration for a {@link Calendar.widget.YearView}
       * - month - Configuration for a {@link Calendar.widget.MonthView}
       * - week - Configuration for a {@link Calendar.widget.WeekView}
       * - day - Configuration for a {@link Calendar.widget.DayView}
       * - list - Configuration for a {@link Calendar.widget.EventList}
       * - resource - Configuration for a {@link Calendar.widget.ResourceView}
       * - dayresource - Configuration for a {@link Calendar.widget.DayResourceView}
       * - dayagenda - Configuration for a {@link Calendar.widget.DayAgendaView}
       * - monthagenda - Configuration for a {@link Calendar.widget.MonthAgendaView}
       *
       * By default, the following modes are included:
       *
       * - day
       * - week
       * - month
       * - year
       * - agenda
       *
       * Any of these may be reconfigured from its default, or omitted from the UI, by configuring the
       * property as `null`:
       *
       * ```javascript
       * modes : {
       *     agenda : {
       *         title : 'Conference events'
       *     },
       *     // Remove year view completely
       *     year : null,
       *     // Add an event list. This type of view is not included by default
       *     list : {
       *         range : 'month'
       *     }
       * }
       * ```
       *
       * Note that the default order of the modes as listed above can be changed by configuring the
       * modes with a `weight` value which causes them to be sorted into ascending weight order.
       *
       * Non-Calendar widgets, outside of those eight types may be specified by including a `type` property
       * in the configuration block. This is the type string of any widget.
       *
       * If using a non-Calendar widget the widget must contain the following properties:
       *
       * - `title` A string used as the button text in the mode activation button.
       * - `description` A string used as the Calendar title when the mode is activated.
       *
       * For example, you might want to use a scheduler as a calendar mode. This can be done like this:
       *
       * ```javascript
       *     modes : {
       *         myScheduler : {
       *             // If the mode name is not a standard Calender view
       *             // we need to specify the type of Widget we are configuring here.
       *             type        : 'scheduler',
       *
       *             // This text goes into the activation button
       *             title : 'Timeline',
       *             columns : [
       *                 { type : 'resourceInfo', field : 'name', text : 'Staff/Resource', width : 175 }
       *             ],
       *
       *             features : {
       *                 nonWorkingTime     : true,
       *                 timeRanges         : true,
       *                 resourceTimeRanges : true
       *             },
       *
       *             workingTime : {
       *                 fromHour : 7,
       *                 toHour   : 22
       *             },
       *         }
       *     }
       * ```
       *
       * @property {ContainerItemConfig} [modes.custom] Settings specific to custom mode. `custom` may be any `String` property within this configuration object.
       * @typings modes.custom -> {[mode: string]}:{ContainerItemConfig|boolean|null|undefined}
       *
       * @property {AgendaViewConfig|Boolean|null} [modes.agenda] Settings specific to `agenda` mode. Specify `null` or `false` to disable this mode.
       * @property {YearViewConfig|Boolean|null} [modes.year] Settings specific to `year` mode. Specify `null` or `false` to disable this mode.
       * @property {MonthViewConfig|Boolean|null} [modes.month] Settings specific to `month` mode. Specify `null` or `false` to disable this mode.
       * @property {WeekViewConfig|Boolean|null} [modes.week] Settings specific to `week` mode. Specify `null` or `false` to disable this mode.
       * @property {DayViewConfig|Boolean|null} [modes.day] Settings specific to `day` mode. Specify `null` or `false` to disable this mode.
       * @property {EventListConfig|Boolean|null} [modes.list] Settings specific to `list` mode. This mode is disabled by default.
       * @property {ResourceViewConfig|Boolean|null} [modes.resource] Settings specific to `resource` mode. This mode is disabled by default.
       * @property {DayResourceViewConfig|Boolean|null} [modes.dayresourceview] Settings specific to `dayresourceview` mode. This mode is disabled by default.
       * @property {DayAgendaViewConfig|Boolean|null} [modes.dayagenda] Settings specific to `dayagenda` mode. This mode is disabled by default.
       * @property {MonthAgendaViewConfig|Boolean|null} [modes.monthagenda] Settings specific to `monthagenda` mode. This mode is disabled by default.
       * @config {Object} [modes]
       * @default
       */
      modes: {
        day: {},
        week: {},
        month: {},
        year: {},
        agenda: {}
      },
      /**
       * Which of the built-in {@link #config-modes} should be the initially active view.
       * @config {'agenda'|'year'|'month'|'week'|'day'|'list'|'resource'|'dayresource'|String}
       * @default week
       */
      mode: {
        $config: "lazy",
        value: "week"
      },
      /**
       * The default settings applied to all child views.
       *
       * This is a dynamic object, and mutations of it will reapply the new value to all
       * child views.
       * @member {Object} modeDefaults
       */
      /**
       * An object to use to configure common properties for all {@link #config-modes} prior to their construction.
       * @config {Object}
       */
      modeDefaults: {
        value: null
        // use the value config so that the changer always runs so that Proxy is created
      },
      /**
       * The {@link #config-sidebar} as an instance of {@link Calendar.widget.Sidebar}.
       * @member {Core.widget.Container} sidebar
       * @readonly
       */
      /**
       * An optional config object to configure the {@link Calendar.widget.Sidebar} which is
       * shown next to the calendar views specified by the {@link #config-modes}.
       *
       * To disable the sidebar, configure it as `null`.
       *
       * To reconfigure it, specify this config value as an object. The following parameters include
       * several commonly reconfigured properties.
       * @config {Object|Boolean}
       * @param {'left'|'right'} [side] Which {@link Calendar.widget.Sidebar#config-side} to dock to.
       * @param {Boolean} [collapsed] May be initially {@link Calendar.widget.Sidebar#config-collapsed}.
       * @param {Object<String,ContainerItemConfig>} [items] Reconfigure or add to the sidebar UI.
       * @param {ContainerItemConfig} [items.custom] Settings specific to custom item.
       * @typings items.custom -> {[mode: string]}:{ContainerItemConfig|null|undefined}
       * @param {DatePickerConfig} [items.datePicker] The {@link Core.widget.DatePicker} may be reconfigured using
       * an object, or configured away using `null`. Its default {@link Core.widget.Widget#config-weight} is 100.
       * @param {TextFieldConfig} [items.eventFilter] The eventFilter is a {@link Core.widget.TextField} which may
       * be used to filter the events by matched name. It may be reconfigured using an object, or configured away
       * using `null`. Its default {@link Core.widget.Widget#config-weight} is 150.
       * @param {ResourceFilterConfig} [items.resourceFilter] The {@link Scheduler.widget.ResourceFilter} may be
       * reconfigured using an object, or configured away using `null`. Its default
       * {@link Core.widget.Widget#config-weight} is 200.
       */
      sidebar: {
        $config: {
          merge: "objects"
        },
        value: {
          type: "sidebar",
          side: "left",
          scrollable: {
            overflowY: true
          },
          internalListeners: {
            dateChange: "up.onCalendarDateChange",
            collapse: "up.onCalendarSidebarToggle",
            expand: "up.onCalendarSidebarToggle"
          }
        }
      },
      /**
       * An optional config object to configure the {@link Calendar.widget.CalendarDatePicker} which is
       * shown in the {@link #property-sidebar} next to the calendar views.
       *
       * To disable the datePicker, configure it as `null`.
       *
       * To reconfigure it, specify this config value as an object.
       *
       * Note that when `showEvents` is set in the `datePicker`, the cells are slightly larger,
       * therefore the `datePicker` takes up more space and so will make the
       * {@link #property-sidebar} a little wider than normal.
       * @config {CalendarDatePickerConfig|Boolean|null}
       * @param {Boolean|'count'|'dots'} [datePicker.showEvents] Displays the presence of events in each cell.
       * values may be:
       *
       * * `false` - Do not show events in cells.
       * * `true` - Show a themeable bullet to indicate the presence of events for a date.
       * * `'count'` - Show a themeable badge containing the event count for a date.
       * * '`dots'` - Show small event-coloured bullets (to a maximum of three) below the date.
       * @param {Boolean} [datePicker.collapsible] `true` to enable collapse into a header.
       * @param {String} [datePicker.title] The text to place in a header above the picker.
       * @param {Boolean} [datePicker.editMonth] `true` to enable setting the date using the controls
       * in the DatePicker header.
       * @param {Boolean|'range'} multiSelect The DatePicker can select {@link Core.widget.DatePicker#config-multiSelect multiple dates}
       * in two modes:
       *  - `true` to mean many discontiguous dates may be selected.
       *  - `'range'` to mean that a start date and end date may be selected.
       *
       * If set up to multi select, views which are not fixed to a date duration will obey the date selection by using a
       * {@link Calendar.widget.DayView#property-dateFilter} to skip days which are not selected.
       *
       * These include:
       * - AgendaView      (mode type `'agenda'`)
       * - DayViews        (view type `'dayview'`) which you add to the modes - The default `day` and `week` modes have a fixed duration.
       * - ResourceView    (view type `'resourceview'`)
       * - DayResourceView (view type `'dayresourceview'`)
       *
       * The `MonthView`, and the `WeekView` and the built-in single day `DayView` __mode__ all have fixed duration and will not participate
       * in this.
       *
       * If you select a discontiguous range, then the views which are amenable to being controlled in this way will
       * switch their range to encompass what you have selected.
       *
       * For example, if you select the first to the last days of a month, the view's {@link Calendar.widget.mixin.DateRangeOwner#property-range}
       * will be set to `'1 month'`. This choice will be maintained, and by navigating using the next and previous tbar buttons, the views
       * will move by exactly one month.
       *
       * If your set of selected dates is enclosed within a two week block, then the {@link Calendar.widget.mixin.DateRangeOwner#property-range}
       * of the views will be set to `{ unit : 'week', magnitude : 2 }`.
       *
       * If you select the 1st, 3th and 5th of the month, and those days are all on one week row, the
       * {@link Calendar.widget.mixin.DateRangeOwner#property-range} of the views will be set to `{ unit : 'week', magnitude : 1 }`.
       *
       * For selection ranges other than exactly one month, navigation in time will be in increments of one week for intuitive navigation
       * up and down the date picker.
       *
       * When navigating in time, the selected block of dates moves and *follows the time navigation* so that there are always
       * dates in view, even if they are empty.
       */
      datePicker: {
        $config: {
          merge: "objects"
        },
        value: {
          weight: 100,
          dayNameFormat: "d1",
          editMonth: null,
          trapFocus: false
        }
      },
      /**
       * By default, the {@link #property-sidebar} expands and collapses taking width from the
       * calendar UI. Configure `overlaySidebar` as `true` to have it start collapsed, and then
       * **overlay** the calendar UI when the expand/collapse button is toggled.
       * @config {Boolean}
       * @default false
       */
      overlaySidebar: null,
      /**
       * The date which this Calendar encapsulates as its active date.
       *
       * This is usually selected by clicking in the {@link #property-sidebar}'s date picker
       * or by navigating the view forwards or backwards in time.
       *
       * If __set__ as a string, the format should be in `YYYY-MM-DD` format.
       *
       * The __read__ value is always a Date.
       *
       * By default, this is also changed by clicking in day cells of the active view.
       *
       * This may be set programatically which will cause the currently active view to navigate to
       * encapsulate that date.
       * @member {Date} date
       * @accepts {Date|String}
       */
      /**
       * The date which the Calendar, its {@link #property-sidebar}'s date picker, and
       * its active view should encapsulate upon creation.
       *
       * If configured as a string, the format should be in `YYYY-MM-DD` format.
       *
       * This defaults to current date unless {@link #state-saving state saving} has been
       * configured to save the date, in which case the calendar will start up showing the date
       * that was saved in state.
       * @config {Date|String}
       */
      date: {
        $config: {
          equal: "date"
        },
        value: /* @__PURE__ */ new Date()
      },
      /**
       * A {@link Core.helper.DateHelper} format string to use to create date output for
       * view descriptions.
       * @prp {String}
       */
      dateFormat: "MMMM DD, YYYY",
      scrollManager: {
        $config: ["lazy", "nullify"],
        value: {
          direction: "vertical"
        }
      },
      viewUpdateDelay: 2e3,
      /**
       * Get/set the calendar's read-only state. When set to `true`, any UIs for modifying data are disabled.
       * @member {Boolean} readOnly
       */
      /**
       * Configure as `true` to make the calendar read-only, by disabling any UIs for modifying data.
       *
       * __Note that checks MUST always also be applied at the server side.__
       * @config {Boolean}
       * @default false
       */
      readOnly: null,
      /**
       * Set to `false` if you don't want to allow events overlapping times for any one resource (defaults to true).
       * @config {Boolean}
       * @private
       * @default
       */
      allowOverlap: true,
      testConfig: {
        viewUpdateDelay: 5,
        // Test environment must be prevented from clicking the UI very fast while network IO
        // is in flight. LoadOnDemand testing was susceptible to this.
        loadMask: {
          text: "L{GridBase.loadMask}",
          showDelay: 0
        }
      },
      /**
       * Configure this as `true` to make picking a date in the {@link #config-sidebar}'s
       * date picker highlight the date cell in the active view.
       *
       * May also be specified as a function which will be passed the date cell element to enable
       * performing customized highlighting.
       *
       * @config {Boolean|Function}
       * @param {Date} date Cell date
       * @returns {Boolean} Returns `true` to make picking a date in a date picker
       */
      highlightDate: null,
      /**
       * If this config is set, then the `gesture` configured (which defaults to `dblclick`) creates a
       * new event at the mouse or touch event's time point.
       *
       * The exact time is rounded to the closest specified `step` value. This value is also used
       * for the {@link Core.widget.TimeField#property-step} value in the {@link Calendar.feature.EventEdit}'s
       * time input field.
       *
       * The duration of the created event is the specified `duration` value.
       *
       * If this is specified as `true`, the `gesture` becomes `dblclick`, and the other properties
       * are the default values listed below.
       *
       * If this is specified as a string, the string becomes the `gesture`, and the other properties
       * are the default values listed below.
       *
       * @property {String} [gesture='dblclick'] The event name which should trigger event creation at the event's position.
       * @property {Function|String} [newName='New Event'] The name of an event created using `autoCreate` or a function to call which yields the name.
       * The following parameters are passed:
       * - <span class="name">view : <span class="type">CalendarMixin</span></span><div class="description">The view in which the event is being created.</div>
       * - <span class="name">startDate : <span class="type">String</span></span><div class="description">The start date of the event being created.</div>
       * - <span class="name">resourceRecord : <span class="type">ResourceModel</span></span><div class="description">The resource of the event being created.</div>
       * @property {String} [step='15 minutes'] The time unit by which to round the start click point of auto created events.
       * _Only for views which have a granularity of less than one day such as `WeekView` and `DayView'_.
       *
       * For views which show whole days, the start defaults to 8am.
       *
       * This is a string in the format required by {@link Core.helper.DateHelper#function-parseDuration-static}.
       *
       * This value is also used for the {@link Core.widget.TimeField#property-step} value in the
       * {@link Calendar.feature.EventEdit}'s time input field.
       *
       * @property {String} [duration='1 hour'] The default duration for auto created events.
       * @property {Number|String} [startHour=8] The default start hour for auto created events
       * in views where the time granularity is one day. In a DayView or WeekView where a mouse event position
       * will translate to a time of day, this is not used.
       *
       * This is the hour of the day to start the event at. It can be fractional or an HH:MM:SS time string.
       * @config {Object|String|Boolean}
       * @default
       */
      autoCreate: {
        gesture: "dblclick",
        newName: "L{Object.newEvent}",
        step: "15 minutes",
        duration: "1 hour",
        startHour: 8
      },
      focusable: false,
      // The common Navigator needs to know this
      eventCls: "b-cal-event",
      /**
       * The week start day to be used throughout this Calendar, 0 meaning Sunday, 6 meaning Saturday.
       *
       * This propagates into all owned {@link #config-modes}.
       *
       * Defaults to {@link Core.helper.DateHelper#property-weekStartDay-static}. When the default value is used, changing locale
       * will dynamically change the week start day for the calendar and all {@link #config-modes}.
       *
       * __If__ this is configured in from the start, then locale values for the week start day
       * will __not__ apply.
       *
       * For example:
       * ```javascript
       * new Calendar({
       *     // Apply to this Calendar only
       *     weekStartDay : 1 // Week starts on Monday
       *     //....
       * });
       * ```
       *
       * @config {Number} [weekStartDay]
       */
      weekStartDay: false,
      /**
       * Configure as `true` to hide {@link #config-nonWorkingDays} for each calendar view
       * @prp {Boolean}
       */
      hideNonWorkingDays: null,
      /**
       * Non-working days as an object where keys are day indices, 0-6 (Sunday-Saturday), and the value is `true`.
       *
       * This propagates into all owned {@link #config-modes}.
       *
       * Defaults to {@link Core.helper.DateHelper#property-nonWorkingDays-static}. When the default value is used, changing locale
       * will dynamically change the week start day for the calendar and all {@link #config-modes}.
       *
       * __If__ this is configured in from the start, then locale values for the week start day
       * will __not__ apply.
       *
       * Both header and event cells for non working days get the CSS class `b-nonworking-day` added.
       *
       * Note that this is independent of and in addition to weekend days. Cells for Saturday and
       * Sunday always get the CSS class `b-weekend` added.
       *
       * For example:
       * ```javascript
       * new Calendar({
       *     // Apply to this Calendar only
       *     nonWorkingDays : {
       *         0 : true // Only Sunday is non-working day
       *     },
       *     //....
       * });
       * ```
       *
       * This may be updated dynamically:
       *
       * ```javascript
       * // Make Mondays non-working. If hideNonWorkingDays is set, Mondays will disappear
       * myCalendar.nonWorkingDays['1'] = true;
       * ```
       *
       * @prp {Object<Number,Boolean>} [nonWorkingDays]
       */
      nonWorkingDays: false,
      coreHours: null,
      /**
       * Event which is used to show context menus via the context menu features
       * {@link Calendar.feature.EventMenu} and {@link Calendar.feature.ScheduleMenu}.
       * Available options are: 'contextmenu', 'click', 'dblclick'.
       * @config {'contextmenu'|'click'|'dblclick'}
       * @default
       * @category Misc
       */
      contextMenuTriggerEvent: "contextmenu",
      /**
       * Set to `true` to listen for CTRL-Z (CMD-Z on Mac OS) keyboard event and trigger undo (redo when SHIFT is pressed).
       * Only applicable when using a {@link Core.data.stm.StateTrackingManager}.
       * @config {Boolean}
       * @default
       * @category Misc
       */
      enableUndoRedoKeys: true,
      /**
       * Path to load resource images from. Used by the resource headers in ResourceView and by
       * {@link Calendar.widget.mixin.CalendarMixin#config-showResourceAvatars} in event-displaying
       * modes.
       *
       * This is used to show resource avatars using the resource's
       * {@link Scheduler/model/ResourceModel#field-image} or
       * {@link Scheduler/model/ResourceModel#field-imageUrl} fields:
       *
       * * `image` represents image name inside the specified `resourceImagePath`,
       * * `imageUrl` represents fully qualified image URL.
       *
       * If an image is not specified for a resource, or the image is not found, the resource's
       * initials will be displayed.
       * @config {String}
       * @category Misc
       */
      resourceImagePath: null,
      /**
       * The minimum date to which the `startDate` of any child view may be navigated.
       * @member {Date} minDate
       */
      /**
       * The minimum date to which the `startDate` of any child view may be navigated.
       * @config {Date|String}
       */
      minDate: null,
      /**
       * The maximum date to which the `endDate` of any child view may be navigated.
       * @member {Date} maxDate
       */
      /**
       * The maximum date to which the `endDate` of any child view may be navigated.
       * @config {Date|String}
       */
      maxDate: null,
      /**
       * If this is set to `true`, then when determining which assigned resource of a multi assigned event
       * to use to create the event UI, the first resource which is still selected in the
       * {@link Calendar.widget.Sidebar#property-resourceFilter} is used.
       *
       * The default is to use the first resource in the assigned list.
       *
       * The resource's {@link Scheduler.model.ResourceModel#field-eventColor} is used as the colour for the
       * event unless the event has its own {@link Scheduler.model.EventModel#field-eventColor}.
       *
       * The resource's {@link Scheduler.model.ResourceModel#field-eventStyle} is used as the base of the style
       * for the event, and the event's {@link Scheduler.model.EventModel#field-style} is applied to it.
       * @config {Boolean}
       * @default false
       */
      filterEventResources: null,
      /**
       * An optional CSS class name to add to calendar date cells which encapsulate the calendar's
       * active {@link #property-date}.
       *
       * There is no theme rule connected to this date, so no built-in rendition of the active date.
       * The application may add view-specific CSS rules to provide visual indications.
       * @prp {String}
       */
      activeDateCls: null,
      /**
       * The default key shortcuts for the Calendar.
       *
       * The default mappings are:
       * - `Home   : 'focusModeSelector'` - Focus the mode selector
       * - `p      : 'shiftPrevious'` - Shift the view to the previous period
       * - `n      : 'shiftNext'` - Shift the view to the next period
       * - `Ctrl+d : 'focusCurrentDate'` - Focus the current date cell in the current active view
       * - `t      : 'shiftToNow'` - Shift the view to today
       * - `g      : 'gotoDate'` - Go to a specific date
       * - `/      : 'focusEventFilter'` - Focus the event filter field
       * - `[0-9]  : 'onDigitKey'` - Switch to the modes by their one-based index in the mode selector
       *
       * These can be overriden if you need to change the mappings. For example a German application might use
       *
       * ```javascript
       * keyMap : {
       *     z : 'shiftPrevious',     // Zurück
       *     w : 'shiftNext',         // Weiter
       *     h : 'shiftToNow'         // Heute
       *     Home : 'focusCurrentDate // Jump into the active view on Home
       * }
       * ```
       *
       * @config {Object<String,KeyMapConfig>}
       */
      keyMap: {
        Enter: "onEnterKey",
        Escape: "onEscapeKey",
        Home: "focusModeSelector",
        p: "shiftPrevious",
        n: "shiftNext",
        t: "shiftToNow",
        g: "gotoDate",
        "Ctrl+d": "focusCurrentDate",
        "/": "focusEventFilter",
        [/\d/]: "onDigitKey"
      },
      gotoDatePopup: {
        $config: "lazy",
        value: {
          cls: "b-calendar-goto-date-popup",
          type: "popup",
          title: "L{Calendar.gotoDate}",
          floating: false,
          positioned: true,
          centered: true,
          items: {
            dateField: {
              type: "datefield"
            }
          },
          bbar: {
            items: {
              cancel: {
                text: "L{Object.Cancel}",
                onClick: "up.close"
              },
              go: {
                text: "L{Object.go}",
                onClick: "up.onGotoDateClick"
              }
            }
          },
          keyMap: {
            Enter: "up.onGotoDateClick"
          }
        }
      },
      /**
       * The names of properties which are propagated down from the Calendar to child views
       * unless the view has its own value for that property.
       * @config {String[]}
       * @advanced
       */
      propagatedConfigs: {
        value: [
          "dateFormat",
          "date",
          "rtl",
          "project",
          "readOnly",
          "weekStartDay",
          "nonWorkingDays",
          "hideNonWorkingDays",
          "coreHours",
          "timeZone",
          "minDate",
          "maxDate",
          "resourceImagePath",
          "visibleStartTime",
          "visibleEndTime",
          "filterEventResources"
        ],
        $config: {
          merge: "distinct"
        }
      }
    };
  }
  static get delayable() {
    return {
      refresh: {
        type: "raf",
        cancelOutstanding: true
      },
      syncUIWithActiveView: {
        type: "buffer",
        delay: 1,
        cancelOutstanding: true
      }
    };
  }
  //endregion
  compose() {
    const { includeWeekendsButton, navigatorPlacement } = this;
    return {
      class: {
        [`b-calendar-nav-${navigatorPlacement || "toolbar"}`]: 1,
        "b-calendar-include-weekends-button": includeWeekendsButton
      }
    };
  }
  //region Overrides
  applyState() {
    this._hidden = true;
    super.applyState(...arguments);
    this._hidden = false;
    this.triggerPaint();
  }
  onPaintOverride() {
  }
  onInternalPaint() {
    if (this.onPaintOverride()) {
      return;
    }
    super.onInternalPaint();
  }
  onShowOverflowPopup({ overflowPopup }) {
    this.element.classList.add("b-overflow-popup-visible");
    overflowPopup.ion({
      hide: () => this.element.classList.remove("b-overflow-popup-visible"),
      once: true
    });
  }
  //endregion
  //region events
  /**
   * Fires before an event is removed. Can be triggered by user pressing [DELETE] or [BACKSPACE] or by the
   * event editor. Can for example be used to display a custom dialog to confirm deletion, in which case
   * records should be "manually" removed after confirmation:
   *
   * ```javascript
   * calendar.on({
   *    beforeEventDelete({ eventRecords, context }) {
   *        // Show custom confirmation dialog (pseudo code)
   *        confirm.show({
   *            listeners : {
   *                onOk() {
   *                    // Remove the events on confirmation
   *                    context.finalize(true);
   *                },
   *                onCancel() {
   *                    // do not remove the events if "Cancel" clicked
   *                    context.finalize(false);
   *                }
   *            }
   *        });
   *
   *        // Prevent default behaviour
   *        return false;
   *    }
   * });
   * ```
   *
   * @event beforeEventDelete
   * @param {Calendar.view.Calendar} source  The Calendar instance
   * @param {Scheduler.model.EventModel[]} eventRecords  The records about to be deleted
   * @param {Object} context  Additional removal context:
   * @param {Function} context.finalize  Function to call to finalize the removal.
   *      Used to asynchronously decide to remove the records or not. Provide `false` to the function to
   *      prevent the removal.
   * @param {Boolean} [context.finalize.removeRecords = true]  Provide `false` to the function to prevent
   *      the removal.
   * @preventable
   */
  /**
   * Fires when a day spanning event is found, and the date to which its encapsulating event bar
   * extends has been calculated.
   *
   * The default result in the event's `propagateEndDate` property may be mutated by a listener.
   *
   * Note that this is an ending point in time, it does *not* refer to a 24 hour block. So setting
   * the `propagateEndDate` to `new Date(2022, 1, 10)` means that the event bar will occupy cells
   * up to and including February 9 2022 and no further.
   *
   * This is relayed from all modes, so a single listener may be used, for example:
   *
   * ```javascript
   * new Calendar({
   *     listeners : {
   *         eventPropagate(eventData) {
   *             // If the event spills into the next day but not further
   *             // then we do not want an extended event bar.
   *             // An arrow will indicate that it continues rightwards.
   *             if (eventData.eventEndDate < DateHelper.add(eventData.date, 2, 'd')) {
   *                 eventData.propagateEndDate = DateHelper.add(DateHelper.clearTime(eventData.eventRecord.startDate), 1, 'd');
   *             }
   *         }
   *     }
   * });
   * ```
   *
   * The `eventEndDate` in the data block may also be changed to override the event's real end date.
   * This will mean that there will be no arrow indicating that the event continues:
   *
   * ```javascript
   * new Calendar({
   *     listeners : {
   *         eventPropagate(eventData) {
   *             // If the event spills into the next day but not further
   *             // then we do not want an extended event bar.
   *             // Because we override the eventEndDate, no arrow will be present
   *             // to indicate any continuation.
   *             if (eventData.eventEndDate < DateHelper.add(eventData.date, 2, 'd')) {
   *                 eventData.propagateEndDate = eventData.eventEndDate = DateHelper.add(DateHelper.clearTime(eventData.eventRecord.startDate), 1, 'd');
   *             }
   *         }
   *     }
   * });
   * ```
   *
   * @event eventPropagate
   * @param {Date} eventEndDate The end date for which to calculate the propagate end date.
   * @param {Date} propagateEndDate The system-calculated end point of the event bar.
   * @param {Boolean} isAllDay `true` if the event is an all day event, or spans multiple days.
   * @param {Boolean} isOverflow `true` if this is being called as part of further propagation.
   * @param {Boolean} overflows `true` if the event extends into future cells.
   * @param {Scheduler.model.EventModel} eventRecord The event record being propagated.
   * @param {Date} date The date from which the event is being propagated.
   */
  /**
   * Fires when a key is pressed while an event bar is focused.
   * @event eventKeyDown
   * @param {Calendar.widget.mixin.CalendarMixin} source The view which triggered the event.
   * @typings source -> {typeof CalendarMixin}
   * @param {Event} domEvent The initiating DOM event.
   * @param {Date} date The date of the UI element which contains the event.
   * @param {HTMLElement} eventElement The UI element which represents the event.
   * @param {Scheduler.model.EventModel} eventRecord The event in which the key event was detected.
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource in which the key event was detected if the UI includes a resource.
   * @param {Boolean} fromOverflowPopup `true` if the interaction was through the source view's
   * {@link Calendar.widget.mixin.DayCellRenderer#property-overflowPopup}.
   */
  /**
   * Fires when an event bar is mouseovered in any view.
   * @event eventMouseOver
   * @param {Calendar.widget.mixin.CalendarMixin} source The view which triggered the event.
   * @typings source -> {typeof CalendarMixin}
   * @param {Event} domEvent The initiating DOM event.
   * @param {Date} date The date of the UI element which contains the event.
   * @param {HTMLElement} eventElement The UI element which represents the event.
   * @param {Scheduler.model.EventModel} eventRecord The event mouseovered.
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource mouseovered if the UI includes a resource.
   * @param {Boolean} fromOverflowPopup `true` if the interaction was through the source view's
   * {@link Calendar.widget.mixin.DayCellRenderer#property-overflowPopup}.
   */
  /**
   * Fires when an event bar is mousedowned in any view.
   * @event eventMouseDown
   * @param {Calendar.widget.mixin.CalendarMixin} source The view which triggered the event.
   * @typings source -> {typeof CalendarMixin}
   * @param {Event} domEvent The initiating DOM event.
   * @param {Date} date The date of the UI element which contains the event.
   * @param {HTMLElement} eventElement The UI element which represents the event.
   * @param {Scheduler.model.EventModel} eventRecord The event mousedowned on.
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource mousedowned on if the UI includes a resource.
   * @param {Boolean} fromOverflowPopup `true` if the interaction was through the source view's
   * {@link Calendar.widget.mixin.DayCellRenderer#property-overflowPopup}.
   */
  /**
   * Fires when an event bar is mouseupped in any view.
   * @event eventMouseUp
   * @param {Calendar.widget.mixin.CalendarMixin} source The view which triggered the event.
   * @typings source -> {typeof CalendarMixin}
   * @param {Event} domEvent The initiating DOM event.
   * @param {Date} date The date of the UI element which contains the event.
   * @param {HTMLElement} eventElement The UI element which represents the event.
   * @param {Scheduler.model.EventModel} eventRecord The event mouseupped.
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource mouseupped if the UI includes a resource.
   * @param {Boolean} fromOverflowPopup `true` if the interaction was through the source view's
   * {@link Calendar.widget.mixin.DayCellRenderer#property-overflowPopup}.
   */
  /**
   * Fires when an event bar is clicked in any view.
   * @event eventClick
   * @param {Calendar.widget.mixin.CalendarMixin} source The view which triggered the event.
   * @typings source -> {typeof CalendarMixin}
   * @param {Event} domEvent The initiating DOM event.
   * @param {Date} date The date of the UI element which contains the event.
   * @param {HTMLElement} eventElement The UI element which represents the event.
   * @param {Scheduler.model.EventModel} eventRecord The event clicked on.
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource clicked on if the UI includes a resource.
   * @param {Boolean} fromOverflowPopup `true` if the interaction was through the source view's
   * {@link Calendar.widget.mixin.DayCellRenderer#property-overflowPopup}.
   */
  /**
   * Fires when an event bar is double clicked in any view.
   * @event eventDblClick
   * @param {Calendar.widget.mixin.CalendarMixin} source The view which triggered the event.
   * @typings source -> {typeof CalendarMixin}
   * @param {Event} domEvent The initiating DOM event.
   * @param {Date} date The date of the UI element which contains the event.
   * @param {HTMLElement} eventElement The UI element which represents the event.
   * @param {Scheduler.model.EventModel} eventRecord The event double clicked on.
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource double clicked on if the UI includes a resource.
   * @param {Boolean} fromOverflowPopup `true` if the interaction was through the source view's
   * {@link Calendar.widget.mixin.DayCellRenderer#property-overflowPopup}.
   */
  /**
   * Fires when an event bar is mouseouted in any view.
   * @event eventMouseOut
   * @param {Calendar.widget.mixin.CalendarMixin} source The view which triggered the event.
   * @typings source -> {typeof CalendarMixin}
   * @param {Event} domEvent The initiating DOM event.
   * @param {Date} date The date of the UI element which contains the event.
   * @param {HTMLElement} eventElement The UI element which represents the event.
   * @param {Scheduler.model.EventModel} eventRecord The event mouseouted.
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource mouseouted if the UI includes a resource.
   * @param {Boolean} fromOverflowPopup `true` if the interaction was through the source view's
   * {@link Calendar.widget.mixin.DayCellRenderer#property-overflowPopup}.
   */
  /**
   * Fires when cursor enters an event bar in any view.
   * @event eventMouseEnter
   * @param {Calendar.widget.mixin.CalendarMixin} source The view which triggered the event.
   * @typings source -> {typeof CalendarMixin}
   * @param {Event} domEvent The initiating DOM event.
   * @param {Date} date The date of the UI element which contains the event.
   * @param {HTMLElement} eventElement The UI element which represents the event.
   * @param {Scheduler.model.EventModel} eventRecord The event record.
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource if the UI includes a resource.
   * @param {Boolean} fromOverflowPopup `true` if the interaction was through the source view's
   * {@link Calendar.widget.mixin.DayCellRenderer#property-overflowPopup}.
   */
  /**
   * Fires when cursor leaves an event bar in any view.
   * @event eventMouseLeave
   * @param {Calendar.widget.mixin.CalendarMixin} source The view which triggered the event.
   * @typings source -> {typeof CalendarMixin}
   * @param {Event} domEvent The initiating DOM event.
   * @param {Date} date The date of the UI element which contains the event.
   * @param {HTMLElement} eventElement The UI element which represents the event.
   * @param {Scheduler.model.EventModel} eventRecord The event record.
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource if the UI includes a resource.
   * @param {Boolean} fromOverflowPopup `true` if the interaction was through the source view's
   * {@link Calendar.widget.mixin.DayCellRenderer#property-overflowPopup}.
   */
  /**
   * Fires when an event bar is right-clicked in any view.
   * @event eventContextMenu
   * @param {Calendar.widget.mixin.CalendarMixin} source The view which triggered the event.
   * @typings source -> {typeof CalendarMixin}
   * @param {Event} domEvent The initiating DOM event.
   * @param {Date} date The date of the UI element which contains the event.
   * @param {HTMLElement} eventElement The UI element which represents the event.
   * @param {Scheduler.model.EventModel} eventRecord The event right-clicked on.
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource right-clicked on if the UI includes a resource.
   * @param {Boolean} fromOverflowPopup `true` if the interaction was through the source view's
   * {@link Calendar.widget.mixin.DayCellRenderer#property-overflowPopup}.
   */
  /**
   * Fires when a day number is clicked in a view which shows day numbers. This will include
   * the cells of a {@link Calendar.widget.YearView YearView}, the cell header of cells
   * in a {@link Calendar.widget.MonthView MonthView}, and the day header in a
   * {@link Calendar.widget.DayView}.
   * @event dayNumberClick
   * @param {Calendar.widget.mixin.CalendarMixin} source The view which triggered the event.
   * @typings source -> {typeof CalendarMixin}
   * @param {Event} domEvent The initiating DOM event.
   * @param {Date} date The date clicked on.
   * @param {DayCell} cellData An object that contains data about the calendar cell for the date.
   * @param {Boolean} fromOverflowPopup `true` if the interaction was through the source view's
   * {@link Calendar.widget.mixin.DayCellRenderer#property-overflowPopup}.
   */
  /**
   * Fires when a week number is clicked. This will include the week number cells of a
   * {@link Calendar.widget.YearView YearView}, the week number in the first cell
   * in each row of a {@link Calendar.widget.MonthView MonthView}
   * @event weekNumberClick
   * @param {Calendar.widget.mixin.CalendarMixin} source The view which triggered the event.
   * @typings source -> {typeof CalendarMixin}
   * @param {Event} domEvent The initiating DOM event.
   * @param {Number[]} week The `[year, week]` clicked on.
   * @param {Date} date The date clicked on.
   */
  /**
   * Fires when a month name header in a {@link Calendar.widget.YearView YearView} is clicked on.
   * @event monthNameClick
   * @param {Calendar.widget.mixin.CalendarMixin} source The view which triggered the event.
   * @typings source -> {typeof CalendarMixin}
   * @param {Event} domEvent The initiating DOM event.
   * @param {Number} month The month index _(zero based)_ clicked on.
   * @param {Date} date The date of the first of the month clicked on.
   */
  /**
   * Fires when a cell overflow indicator is clicked in any view. This will include
   * the cells of a {@link Calendar.widget.YearView YearView} and the `+ n more` overflow
   * indicator of cells in a {@link Calendar.widget.MonthView MonthView}, and "all day" events section
   * of a {@link Calendar.widget.DayView}.
   * @event cellOverflowClick
   * @param {Calendar.widget.mixin.CalendarMixin} source The view which triggered the event.
   * @typings source -> {typeof CalendarMixin}
   * @param {Event} domEvent The initiating DOM event.
   * @param {Date} date The date clicked on.
   */
  /**
   * Fires when a mouseover made its way through to an empty part of any view.
   * @event scheduleMouseOver
   * @param {Calendar.widget.mixin.CalendarMixin} source The view which triggered the event.
   * @typings source -> {typeof CalendarMixin}
   * @param {Event} domEvent The initiating DOM event.
   * @param {Date} date The date mouseovered. _Note that in a {@link Calendar.widget.DayView},
   * this will include granular time information. For other views, this is the *start* of the date.
   */
  /**
   * Fires when a mousedown made its way through to an empty part of any view.
   * @event scheduleMouseDown
   * @param {Calendar.widget.mixin.CalendarMixin} source The view which triggered the event.
   * @typings source -> {typeof CalendarMixin}
   * @param {Event} domEvent The initiating DOM event.
   * @param {Date} date The date mousedowned on. _Note that in a {@link Calendar.widget.DayView},
   * this will include granular time information. For other views, this is the *start* of the date.
   */
  /**
   * Fires when a mouseup made its way through to an empty part of any view.
   * @event scheduleMouseUp
   * @param {Calendar.widget.mixin.CalendarMixin} source The view which triggered the event.
   * @typings source -> {typeof CalendarMixin}
   * @param {Event} domEvent The initiating DOM event.
   * @param {Date} date The date mouseupped on. _Note that in a {@link Calendar.widget.DayView},
   * this will include granular time information. For other views, this is the *start* of the date.
   */
  /**
   * Fires when a click made its way through to an empty part of any view.
   * @event scheduleClick
   * @param {Calendar.widget.mixin.CalendarMixin} source The view which triggered the event.
   * @typings source -> {typeof CalendarMixin}
   * @param {Event} domEvent The initiating DOM event.
   * @param {Date} date The date clicked on. _Note that in a {@link Calendar.widget.DayView},
   * this will include granular time information. For other views, this is the *start* of the date.
   */
  /**
   * Fires when a dblclick made its way through to an empty part of any view.
   * @event scheduleDblClick
   * @param {Calendar.widget.mixin.CalendarMixin} source The view which triggered the event.
   * @typings source -> {typeof CalendarMixin}
   * @param {Event} domEvent The initiating DOM event.
   * @param {Date} date The date double clicked on. _Note that in a {@link Calendar.widget.DayView},
   * this will include granular time information. For other views, this is the *start* of the date.
   */
  /**
   * Fires when a context menu made its way through to an empty part of any view.
   * @event scheduleContextMenu
   * @param {Calendar.widget.mixin.CalendarMixin} source The view which triggered the event.
   * @typings source -> {typeof CalendarMixin}
   * @param {Event} domEvent The initiating DOM event.
   * @param {Date} date The date clicked on. _Note that in a {@link Calendar.widget.DayView},
   * this will include granular time information. For other views, this is the *start* of the date.
   */
  /**
   * Fires when a mouseout made it's way through to an empty part of any view.
   * @event scheduleMouseOut
   * @param {Calendar.widget.mixin.CalendarMixin} source The view which triggered the event.
   * @typings source -> {typeof CalendarMixin}
   * @param {Event} domEvent The initiating DOM event.
   * @param {Date} date The date mouseouted. _Note that in a {@link Calendar.widget.DayView},
   * this will include granular time information. For other views, this is the *start* of the date.
   */
  /**
   * Fires when one of the views in this Calendar refreshes.
   * @event refresh
   * @param {Calendar.widget.mixin.CalendarMixin} source The view which refreshed.
   * @typings source -> {typeof CalendarMixin}
   */
  /**
   * Fired when an {@link Calendar.widget.mixin.CalendarMixin#config-autoCreate} gesture has
   * created a new event and added it to the event store.
   *
   * If the {@link Calendar.feature.EventEdit} feature is present, it listens for
   * this event and initiates an edit operation. Adding a high `prio` listener which
   * returns `false` can prevent this event from reaching the `eventEdit` processing.
   * @event eventAutoCreated
   * @param {Calendar.widget.mixin.CalendarMixin} source The child view on which the event was initiated.
   * @typings source -> {typeof CalendarMixin}
   * @param {Scheduler.model.EventModel} eventRecord The new event record.
   */
  /**
   * Fired when an empty cell content area is clicked on in a {@link Calendar.widget.MonthView} or a
   * {@link Calendar.widget.CalendarRow}.
   * @event emptyCellClick
   * @param {Event} domEvent The triggering DOM event.
   * @param {Date} date The date which has no visible events
   */
  /**
   * This event fires whenever a child view's {@link Calendar.widget.mixin.CalendarMixin#config-autoCreate
   * autoCreate gesture} is detected and also when a {@link Calendar.feature.CalendarDrag
   * drag-create} gesture is detected.
   *
   * This event is preventable and may be used to validate UI-initiated event creation.
   * @event beforeAutoCreate
   * @preventable
   * @param {Event} domEvent The DOM event which initiated the creation.
   * @param {Date} date The starting time of the event to be created.
   * @param {Scheduler.model.ResourceModel} resourceRecord The resource if the UI includes a resource.
   * @param {Calendar.widget.mixin.CalendarMixin} view The view in which the event is being created.
   * @typings view -> {typeof CalendarMixin}
   */
  /**
   *
   * Fired by the active view after one day cell's events are collected in sorted order according to the
   * {@link Calendar.widget.mixin.CalendarMixin#config-eventSorter}
   *
   * An application may use this to intervene in the event load received by the UI
   * by mutating the `events` array.
   * @event dayCellPopulated
   * @param {Scheduler.model.EventModel[]} events The events to be shown for the passed date
   * @param {Date} The date the events are to be shown in.
   * @param {Calendar.widget.mixin.CalendarMixin} view The view in which the event is being created.
   * @typings view -> {typeof CalendarMixin}
   */
  //endregion
  static getStartAndEndDates(view, eventRecord, dateFormat = "dddd Do MMMM", timeFormat = view.timeFormat || "LT", extendAllDayEndDay = view == null ? void 0 : view.extendAllDayEndDay) {
    const dayTime = view.dayTime || (view.dayTime = new DayTime(0)), {
      allDay,
      startDate
    } = eventRecord, endDate = eventRecord.endDate || DateHelper.add(startDate, eventRecord.fullDuration), endTime = allDay ? extendAllDayEndDay ? DateHelper.ceil(endDate, "1 day") : DateHelper.add(endDate, -1, "day") : endDate, multiDay = dayTime.startOfDay(startDate) < dayTime.startOfDay(endTime), hasTime = !allDay && DateHelper.clearTime(startDate) < startDate || DateHelper.clearTime(endDate) < endTime, format2 = hasTime ? multiDay ? `${dateFormat} ${timeFormat}` : timeFormat : dateFormat, onlyStartDate = eventRecord.isMilestone || allDay && !multiDay;
    return {
      multiDay,
      startDate,
      startText: DateHelper.format(startDate, format2),
      endDate: endTime,
      endText: onlyStartDate ? null : DateHelper.format(endTime, format2)
    };
  }
  static get featureable() {
    return {
      factory: CalendarFeature
    };
  }
  // Gets current date time, time zone converted if time zone is set
  get dateTimeNow() {
    if (this.timeZone != null) {
      return TimeZoneHelper.toTimeZone(/* @__PURE__ */ new Date(), this.timeZone);
    }
    return /* @__PURE__ */ new Date();
  }
  // Gets current date with day info only. Time zone converted if time zone is set
  get today() {
    const today = this.dateTimeNow;
    today.setHours(0, 0, 0, 0);
    return today;
  }
  finalizeInit() {
    var _a5;
    super.finalizeInit();
    const me = this, hideNonWorkingDays = (_a5 = me.activeView) == null ? void 0 : _a5.hideNonWorkingDays;
    if (hideNonWorkingDays != null && me.hideNonWorkingDays == null) {
      me.hideNonWorkingDays = hideNonWorkingDays;
    }
  }
  toggleEmptyText() {
    const { crudManager } = this, { eventStore } = crudManager || this;
    DomHelper.toggleClasses(this.contentElement, "b-calendar-empty", !(eventStore.count || (crudManager == null ? void 0 : crudManager.isLoading)));
  }
  /**
   * Schedules a refresh of the UI for the next animation frame. This is a useful method to call when
   * making multiple data changes, so that each change merely *schedules* a refresh for the next AF and
   * DOM churn is kept to a minimum.
   */
  refresh() {
    this.eachView((v) => {
      var _a5;
      return (_a5 = v.refresh) == null ? void 0 : _a5.call(v);
    });
  }
  /**
   * The {@link #property-modes} as an Array.
   * @property {CalendarView[]}
   */
  get views() {
    const { modes } = this;
    return modes ? Object.values(modes) : [];
  }
  /**
   * Returns the currently active mode as a {@link CalendarView} instance.
   *
   * Similar to the {@link #property-mode} property which is the active mode's name as a string.
   * @property {CalendarView}
   */
  get activeView() {
    return this.viewContainer.layout.activeItem;
  }
  /**
   * Returns the lowest level of active view in the mode container.
   *
   * For example, if a {@link Calendar.widget.ResourceView} is the active view, this would
   * return the sub view of that which is currently focused. Or the resourceView if none
   * of the sub views were focused.
   * @property {CalendarView}
   */
  get activeSubView() {
    var _a5;
    const { activeView } = this, { items: items2 } = activeView, activeSubView = (_a5 = items2.filter(isFocusedCalendarMixin2)) == null ? void 0 : _a5[0];
    return activeSubView || activeView;
  }
  captureFocus() {
    return this.activeView.captureFocus();
  }
  /**
   * Returns the event record for a DOM element or DOM event.
   * @param {HTMLElement|Event} elementOrEvent The DOM node to lookup, or a DOM event whose target to lookup.
   * @returns {Scheduler.model.EventModel} The event record
   */
  getEventRecord(elementOrEvent) {
    return this.activeView.getEventRecord(elementOrEvent);
  }
  /**
   * Returns the event record for a DOM element or DOM event.
   * @param {HTMLElement|Event} elementOrEvent The DOM node to lookup, or a DOM event whose target to lookup.
   * @returns {Scheduler.model.EventModel} The event record
   */
  resolveEventRecord(elementOrEvent) {
    return this.getEventRecord(elementOrEvent);
  }
  onBeforeViewContainerCardChange({
    prevActiveItem,
    activeItem
  }) {
    var _a5, _b2;
    if (this.trigger("beforeActiveItemChange", arguments[0]) === false) {
      return false;
    }
    (_b2 = (_a5 = prevActiveItem._overflowPopup) == null ? void 0 : _a5.hide) == null ? void 0 : _b2.call(_a5);
    if ("date" in activeItem && activeItem.syncViewDate !== false) {
      const date2 = this.date || prevActiveItem.date;
      if (date2) {
        if (activeItem.isCalendarMixin) {
          activeItem.date = date2;
        } else {
          activeItem.whenVisible(() => activeItem.date = date2);
        }
      }
    }
  }
  onViewContainerCardChange(event) {
    var _a5;
    this.trigger("activeItemChange", event);
    if (event.containedFocus) {
      const { activeItem } = event;
      (_a5 = activeItem.focusDate || activeItem.focus) == null ? void 0 : _a5.call(activeItem, this.date);
    }
  }
  onToggleSidebarClick() {
    const { sidebar } = this, { collapsible } = sidebar;
    if (collapsible == null ? void 0 : collapsible.isPanelCollapserOverlay) {
      if (!collapsible.revealing) {
        collapsible.toggleReveal(!sidebar.revealed);
      }
    } else {
      sidebar.toggleCollapsed();
    }
  }
  // keyMap handler for the Enter
  onEnterKey(event) {
    const { target } = event, view = _Calendar.fromElement(target), { dayContentElement } = view, role = target.getAttribute("role"), eventProps3 = view.isDayView ? { clientY: dayContentElement.getBoundingClientRect().top + view.hourHeight * view.autoCreate.startHour - dayContentElement.scrollTop } : {};
    if (role === "link" || role === "columnheader" || target.matches(".b-calendar-cell,.b-day-cell")) {
      DomHelper.triggerMouseEvent(target, role === "link" ? "click" : "dblclick", eventProps3);
      event.stopPropagation();
    }
    return false;
  }
  // keyMap handler for the Escape key
  onEscapeKey(event) {
    var _a5;
    if (!this.element.matches(".b-draghelper-active")) {
      (_a5 = DomHelper.findParent(event.target, isNavigable, this.element)) == null ? void 0 : _a5.focus();
      event.preventDefault();
      event.stopPropagation();
    }
    return false;
  }
  // keyMap handler for the 1-9 keys
  onDigitKey(keyEvent) {
    if (!DomHelper.isEditable(keyEvent.target) && keyEvent.key.match(/\d/)) {
      this.mode = this.viewContainer.items[Number(keyEvent.key) - 1];
    }
    return false;
  }
  // keyMap handler for the Home key
  focusModeSelector() {
    var _a5, _b2;
    const widgetMap = (_a5 = this.widgetMap.modeSelector) == null ? void 0 : _a5.widgetMap;
    widgetMap && ((_b2 = [
      widgetMap[`${this.activeView.modeName}ShowButton`],
      widgetMap.modeSelectorButton
    ].find((w) => w == null ? void 0 : w.isFocusable)) == null ? void 0 : _b2.focus());
  }
  // keyMap handler for the G key
  gotoDate() {
    const { gotoDatePopup } = this;
    gotoDatePopup.widgetMap.dateField.value = this.date;
    gotoDatePopup.show();
  }
  changeGotoDatePopup(gotoDatePopup) {
    return Popup.create(Popup.mergeConfigs({
      appendTo: this.contentElement
    }, gotoDatePopup));
  }
  onGotoDateClick() {
    var _a5;
    const {
      gotoDatePopup,
      activeView
    } = this, { dateField } = gotoDatePopup.widgetMap;
    gotoDatePopup.hide();
    if (dateField.isValid) {
      this.date = dateField.value;
      (_a5 = activeView.focusDate || activeView.focus) == null ? void 0 : _a5.call(activeView, this.date);
    }
  }
  focusEventFilter() {
    var _a5;
    (_a5 = this.widgetMap.eventFilter) == null ? void 0 : _a5.focus();
  }
  focusCurrentDate() {
    var _a5;
    const { activeView } = this;
    (_a5 = activeView.focusDate || activeView.focus) == null ? void 0 : _a5.call(activeView, this.date);
  }
  /**
   * Navigates Calendar's {@link #property-activeView active view} to the current local date
   */
  shiftToNow() {
    var _a5, _b2;
    const me = this, { activeView } = me, datePicker = (_a5 = me.sidebar) == null ? void 0 : _a5.widgetMap.datePicker, { dayTime } = activeView, now2 = /* @__PURE__ */ new Date(), today = dayTime ? dayTime.startOfDay(now2) : DateHelper.clearTime(now2);
    me.date = today;
    if (activeView.isSchedulerBase) {
      activeView.updateDate(today, today);
    } else {
      activeView.scrollTo(today, { y: !activeView.isDayView });
    }
    datePicker && (datePicker.date = today);
    me.updateViewDescription();
    (_b2 = activeView.focusDate || activeView.focus) == null ? void 0 : _b2.call(activeView, today);
  }
  /**
   * Navigates back in time in a step unit specific to the Calendar's {@link #property-activeView active view}
   * if the new date does not violate the Calendar's {@link #config-minDate}.
   */
  shiftPrevious(domEvent) {
    var _a5;
    const { activeItem } = this.viewContainer.layout, { startDate } = activeItem;
    if (activeItem == null ? void 0 : activeItem.previous) {
      activeItem.previous();
      this.updateViewDescription(activeItem);
      if (activeItem.startDate - startDate) {
        if (activeItem.animateTimeShift) {
          DomHelper.slideIn(activeItem.contentElement, -1);
        }
      }
      if (domEvent == null ? void 0 : domEvent.type.startsWith("key")) {
        (_a5 = activeItem.focusDate || activeItem.focus) == null ? void 0 : _a5.call(activeItem, this.date);
      }
    }
  }
  /**
   * Navigates forward in time in a step unit specific to the Calendar's {@link #property-activeView active view}
   * if the new date does not violate the Calendar's {@link #config-maxDate}.
   */
  shiftNext(domEvent) {
    var _a5;
    const { activeItem } = this.viewContainer.layout, { startDate } = activeItem;
    if (activeItem == null ? void 0 : activeItem.next) {
      activeItem.next();
      this.updateViewDescription(activeItem);
      if (activeItem.startDate - startDate) {
        if (activeItem.animateTimeShift) {
          DomHelper.slideIn(activeItem.contentElement, 1);
        }
      }
      if (domEvent == null ? void 0 : domEvent.type.startsWith("key")) {
        (_a5 = activeItem.focusDate || activeItem.focus) == null ? void 0 : _a5.call(activeItem, this.date);
      }
    }
  }
  onCalendarSidebarToggle(event) {
    event.source = this;
    this.trigger(`sidebar${StringHelper.capitalize(event.type)}`, event);
  }
  async onCalendarDateChange({ date: date2, oldDate, userAction }) {
    var _a5, _b2, _c2, _d;
    const me = this, { layout } = me.viewContainer, { activeItem } = layout.isChangingCard ? layout.animateDetacher.event : layout, {
      highlightDate
    } = me;
    if (activeItem && activeItem.syncViewDate !== false && "date" in activeItem) {
      const { getDayElement } = activeItem;
      if (!DateHelper.betweenLesser(date2, activeItem.startDate, activeItem.endDate)) {
        (_b2 = (_a5 = activeItem._overflowPopup) == null ? void 0 : _a5.hide) == null ? void 0 : _b2.call(_a5);
      }
      activeItem.date = date2;
      if (userAction && !(activeItem.isAgendaView || activeItem.isYearView || activeItem.isSchedulerBase)) {
        (_c2 = activeItem.scrollTo) == null ? void 0 : _c2.call(activeItem, date2, {
          y: !activeItem.isDayView
        });
      }
      date2 = me.date = activeItem.date;
      if (userAction && highlightDate && getDayElement) {
        if (activeItem.scrollTo) {
          await activeItem.scrollTo(date2);
          if (activeItem.scrolledToDate) {
            date2 = activeItem.scrolledToDate;
          }
        }
        const cell = (_d = activeItem.getDayElement) == null ? void 0 : _d.call(activeItem, date2);
        if (cell) {
          if (isNaN(highlightDate)) {
            me.callback(highlightDate, me, [cell, me]);
          } else {
            DomHelper.highlight(Rectangle.from(cell));
          }
        }
      }
    }
    me.syncViewActiveDates(oldDate, date2);
    me.trigger("dateChange", { oldDate, date: date2 });
  }
  updateActiveDateCls(activeDateCls, oldActiveDateCls) {
    this.syncViewActiveDates(this.date, this.date, oldActiveDateCls);
  }
  syncViewActiveDates(oldDate, date2 = this.date, oldActiveDateCls = this.activeDateCls) {
    const me = this, { activeDateCls } = me, { contentElement } = me.viewContainer;
    if (oldActiveDateCls) {
      const oldDateCells = oldDate ? contentElement.querySelectorAll(`[data-date="${DateHelper.makeKey(oldDate)}"],[role="gridcell"][data-header-date="${DateHelper.makeKey(oldDate)}"]`) : emptyArray16;
      for (let i = 0, { length } = oldDateCells; i < length; i++) {
        oldDateCells[i].classList.remove(oldActiveDateCls);
      }
    }
    if (activeDateCls) {
      const newDateCells = contentElement.querySelectorAll(`[data-date="${DateHelper.makeKey(date2)}"],[role="gridcell"][data-header-date="${DateHelper.makeKey(date2)}"]`);
      for (let i = 0, { length } = newDateCells; i < length; i++) {
        newDateCells[i].classList.add(activeDateCls);
      }
    }
  }
  updateViewDescription(activeView = this.activeView) {
    const me = this, {
      tbar,
      widgetMap,
      element
    } = me, { viewDescription } = widgetMap, description = (activeView == null ? void 0 : activeView.description) || "Calendar", descriptionContent = `<span class="b-calendar-view-desc-text">${description}</span>`;
    element.dataset.mode = me.activeView.modeName;
    element.setAttribute("aria-label", description);
    if (activeView) {
      if (tbar && viewDescription) {
        if (viewDescription.content !== descriptionContent) {
          viewDescription.content = descriptionContent;
        }
      }
      const viewDate = activeView.date || activeView.startDate;
      if (viewDate) {
        this.date = viewDate;
      }
    }
  }
  getStepUnitText(activeView = this.activeView) {
    return activeView.stepUnit;
  }
  syncUIWithActiveView(activeView = this.activeView) {
    const me = this, { tbar, sidebar, widgetMap } = me;
    if (tbar && (activeView == null ? void 0 : activeView.isVisible)) {
      const {
        prevButton,
        nextButton,
        modeSelector
      } = widgetMap, stepUnitText = me.getStepUnitText(activeView);
      modeSelector == null ? void 0 : modeSelector.syncActiveMode(activeView);
      if (sidebar) {
        sidebar.stepUnitText = stepUnitText;
      }
      me.updateViewDescription(activeView);
      if (prevButton) {
        prevButton.tooltip = stepUnitText ? me.L("L{previous}", stepUnitText) : "";
        prevButton.disabled = !stepUnitText || !activeView.previous;
      }
      if (nextButton) {
        nextButton.tooltip = stepUnitText ? me.L("L{next}", stepUnitText) : "";
        nextButton.disabled = !stepUnitText || !activeView.next;
      }
    }
  }
  updateEventStore(eventStore) {
    this.detachListeners("eventStoreDateRange");
    this.features.loadOnDemand;
    this.processConfiguredListeners();
    eventStore.ion({
      loadDateRange: "onEventStoreDateRangeRequested",
      name: "eventStoreDateRange",
      thisObj: this
    });
    if (this.sidebar) {
      this.sidebar.eventStore = eventStore;
    }
  }
  /**
   * Executes the passed function for each child calendar view in {@link #config-modes}
   * @param {Function|String} fn The function - or name of a function in the view's ownership hierarchy - to call.
   * @param {CalendarView} fn.view The view being called.
   * @param {Object[]} [args] The arguments to pass. Defaults to the view being called.
   * @param {Object} [thisObj] The `this` reference for the function. Defaults to the view being called.
   */
  eachView(fn2, args, thisObj = null) {
    const passView = args == null;
    for (const view of this.views) {
      if (passView) {
        args = [view];
      }
      if (view.callback(fn2, thisObj || view, args) === false) {
        return;
      }
    }
  }
  onEventStoreDateRangeRequested(event) {
    if (event.changed) {
      this.trigger("dateRangeChange", event);
    }
    this.trigger("dateRangeRequested", event);
  }
  async onModeSelectorToggle({ source: button, pressed }) {
    if (pressed) {
      const me = this, { layout } = me.viewContainer, { view } = button;
      await me.modeSelectionPromise;
      me.modeSelectionPromise = layout.setActiveItem(view).promise;
    }
  }
  /**
   * Sets and gets which of the configured {@link #property-modes} is the current active view.
   *
   * When read, this yields the string name of the currently active mode.
   *
   * When setting, this accepts either the name, or the actual widget instance:
   *
   * ```javascript
   * calendar.mode = 'week';
   * ```
   *
   * or
   *
   * ```javascript
   * calendar.mode = calendar.modes.agenda;
   * ```
   * @member {String} mode
   */
  updateMode(mode, was) {
    const me = this, { isConfiguring } = me, { layout } = me.viewContainer, configs = me.initialConfig || {};
    if (!(isConfiguring || me._responsiveUpdating && ("mode" in configs || "activeIndex" in configs))) {
      mode = typeof mode === "string" ? me.modes[mode] : mode;
      if (mode) {
        if (mode !== me.activeView) {
          layout.setActiveItem(mode, void 0, {
            animation: !me.isResponsiveUpdating
          });
          if (!isConfiguring && layout.activeItem !== mode) {
            me._mode = was;
            me.syncUIWithActiveView();
          }
        }
      } else {
        me._mode = was;
      }
    }
  }
  // Handle eventStore load or full datachange.
  // It will have refreshed all our views, so keep the Calendar UI synced.
  onEventStoreRefresh() {
    if (!this.isConfiguring) {
      this.syncUIWithActiveView(this.viewContainer.layout.activeItem);
    }
  }
  handleViewRangeChange() {
    this.syncUIWithActiveView();
  }
  handleViewPaint({ source: view }) {
    this.syncUIWithActiveView(view);
    this._mode = view.modeName;
    this.trigger("viewPaint", { ...arguments[0] });
  }
  onViewRefresh({ source: view }) {
    var _a5;
    (_a5 = super.onViewRefresh) == null ? void 0 : _a5.call(this, ...arguments);
    if (view.isVisible) {
      if (view === this.activeView) {
        this.updateViewDescription(view);
      } else if (this.activeView.owns(view)) {
        this.syncUIWithActiveView(this.activeView);
      }
    }
    this.syncViewActiveDates();
  }
  updateEventCls(eventCls) {
    if (!this.initialConfig.eventInnerSelector) {
      this.eventInnerSelector = `.${eventCls}`;
    }
    if (!this.initialConfig.eventSelector) {
      this.eventSelector = `${this.eventInnerSelector}-wrap`;
    }
  }
  changeModes(modes, was) {
    var _a5, _b2;
    const me = this, { modeDefaults } = me, modeArray = [];
    me.getConfig("crudManager", "defaultCalendar", "eventCls");
    let modeConfig;
    for (const modeType in modes) {
      modeConfig = modes[modeType];
      if (modeConfig) {
        modeArray.push(ObjectHelper.assign({
          modeType,
          weight: (_b2 = (_a5 = modeConfig.weight) != null ? _a5 : modeDefaults == null ? void 0 : modeDefaults.weight) != null ? _b2 : 0
        }, modeConfig));
      }
    }
    if (modeArray.some(hasWeight)) {
      modes = {};
      modeArray.sort(Widget.weightSortFn);
      for (let i = 0, { length } = modeArray; i < length; i++) {
        modeConfig = modeArray[i];
        modes[modeConfig.modeType] = modeConfig;
      }
    }
    const { tbar, element } = me, listeners = {
      thisObj: me,
      // When a mode has an internal change that requires the description shown in the tbar to be refreshed
      descriptionChange: "onViewDescriptionChange",
      // These are for implementing drilling from higher to lower level views
      weekNumberClick: "onViewWeekNumberClick",
      monthNameClick: "onViewMonthNameClick",
      dayNumberClick: "onViewDayNumberClick",
      // Handle a double click gesture inside a scheduler child mode
      beforeEventAdd: "onViewBeforeEventAdd",
      // This is how the Calendar relays events from modes to the outside world
      // The Calendar listeners get first look at the events so that app code gets
      // a chance to intervene early.
      catchAll: {
        fn: "onViewCatchAll",
        prio: 1e4
      },
      // Schedulers fire this when their date range changes
      dateRangeChange: "handleViewRangeChange",
      // UI also needs updating if a DateRangeOwner has its shiftIncrement changed
      shiftIncrementChange: "handleViewRangeChange",
      rangeChange: "handleViewRangeChange",
      paint: "handleViewPaint",
      refresh: "onViewRefresh"
    }, manager = me.$modes || (me.$modes = new DynamicObject({
      factory: Modes,
      inferType: true,
      // the name of a mode is its default type (allow its config object to override it)
      owner: me,
      // All instances have this.calendar pointing to this
      ownerName: "calendar",
      created(mode, name) {
        var _a6, _b3;
        mode.modeName = name;
        if (mode.isSchedulerBase) {
          const { features } = mode, { eventEdit } = me.features;
          if (eventEdit) {
            eventEdit.applyPluginConfig(mode);
            mode.editEvent = me.editEvent.bind(me);
          }
          mode.ion({ renderrow: () => me.syncUIWithActiveView() });
          if (!isConfiguredAway(features, "timeAxisHeaderMenu")) {
            const { listRangeMenu: menu } = mode;
            ObjectHelper.setPath(features.timeAxisHeaderMenu || (features.timeAxisHeaderMenu = {}), "items.dateRange", false);
            if (!isConfiguredAway(features.timeAxisHeaderMenu.items, "setRange")) {
              features.timeAxisHeaderMenu.items.setRange = {
                text: "L{Calendar.timeRange}",
                icon: "b-fw-icon b-icon-calendar",
                weight: 300,
                menu
              };
            }
            if ((_a6 = me.sidebar) == null ? void 0 : _a6.widgetMap.eventFilter) {
              features.timeAxisHeaderMenu.items.eventsFilter = false;
            }
          }
        }
        element.classList.add(`b-cal-${name}`);
        mode.ion(listeners);
        me.relayEvents(mode, ["eventPropagate"]);
        if (!me.isConfiguring) {
          me.trigger("addMode", { mode });
        }
        const modeSelector = (_b3 = tbar == null ? void 0 : tbar.widgetMap) == null ? void 0 : _b3.modeSelector;
        if (modeSelector) {
          modeSelector.calendar = me;
          modeSelector.addMode(name, mode);
        }
        if (!ObjectHelper.getPropertyDescriptor(mode, "eventCount")) {
          Object.defineProperty(mode, "eventCount", {
            get() {
              return this.eventStore.getEvents({
                startDate: this.startDate,
                endDate: this.endDate
              }).length;
            }
          });
        }
      },
      setup(config, name) {
        const { defaultCalendar } = me, viewType = Modes.resolveType(config.type);
        if (modeDefaults) {
          config = ObjectHelper.merge({}, modeDefaults, config);
        }
        if (viewType.isSchedulerBase) {
          const features = config.features || (config.features = {});
          features.eventTooltip = features.eventMenu = features.eventEdit = features.scheduleMenu = false;
          if (!("syncCalendarDate" in config)) {
            config.syncCalendarDate = true;
          }
        } else {
          config.eventSelector = me.eventSelector;
          config.eventInnerSelector = me.eventInnerSelector;
        }
        if (config.type === "list") {
          config.selectedRecordCollection = me.selectedCollection;
        }
        if ("autoCreate" in me.initialConfig && !("autoCreate" in config)) {
          config.autoCreate = me.autoCreate;
        }
        if (ObjectHelper.hasOwn(me, "_defaultCalendar")) {
          config.defaultCalendar = me._defaultCalendar;
        } else if (defaultCalendar != null) {
          config.defaultCalendar = defaultCalendar;
        }
        inheritConfigs(config, me, me.propagatedConfigs);
        return me.trigger("beforeCreateMode", { config }) === false ? null : config;
      }
    }));
    manager.update(modes);
    if (!was) {
      return manager.target;
    }
  }
  changeModeDefaults(modeDefaults) {
    const me = this, result = new Proxy({ ...modeDefaults }, {
      set(target, prop, value2) {
        const result2 = Reflect.set(...arguments);
        me.updateModeDefaults({
          [prop]: value2
        });
        return result2;
      },
      deleteProperty(target, prop) {
        const result2 = Reflect.deleteProperty(...arguments);
        me.updateModeDefaults({
          [prop]: null
        });
        return result2;
      }
    });
    return result;
  }
  updateModeDefaults(modeDefaults) {
    if (!this.isConfiguring) {
      this.eachView((v) => v.setConfig(modeDefaults));
    }
  }
  get collapsibleConfig() {
    const overlaySidebar = this.peekConfig("overlaySidebar"), config = {
      type: overlaySidebar ? "overlay" : "inline",
      tool: null
    };
    if (overlaySidebar) {
      config.recollapseTool = null;
    }
    return config;
  }
  updateOverlaySidebar(overlaySidebar) {
    const { sidebar, collapsibleConfig } = this, collapsible = sidebar == null ? void 0 : sidebar.collapsible;
    if (collapsible) {
      if (collapsible.type !== collapsibleConfig.type) {
        sidebar.collapsible = collapsibleConfig;
      }
      if (overlaySidebar) {
        if (!sidebar.collapsed) {
          sidebar.collapsed = true;
          sidebar.collapsedDueToOverlay = true;
        }
      } else if (sidebar.collapsedDueToOverlay || sidebar.isVisible && collapsible.isPanelCollapserOverlay) {
        sidebar.collapsed = false;
        sidebar.collapsedDueToOverlay = false;
      }
    }
  }
  changeSidebar(config, sidebar) {
    var _a5, _b2;
    const me = this, { datePicker, statefulId: stateId } = me;
    if (config) {
      const sidebarItems = config.items || (config.items = {}), sidebarDatePicker = sidebarItems.datePicker;
      if (datePicker) {
        if (sidebarDatePicker !== null && sidebarDatePicker !== false) {
          sidebarItems.datePicker = _Calendar.mergeConfigs(sidebarItems.datePicker, datePicker);
        }
      } else {
        sidebarItems.datePicker = null;
      }
      return Sidebar.reconfigure(sidebar, config, {
        owner: me,
        setup: (cfg, type) => me.setupWidgetConfig(cfg, type),
        // These are combined with "config" prior to calling our setup() above (so they are part of the "cfg"
        // parameter passed to setup():
        defaults: {
          stateId: stateId && `${stateId}:sidebar`,
          eventStore: me.eventStore,
          date: me.date,
          weight: (config == null ? void 0 : config.side) === "right" || (config == null ? void 0 : config.side) === "end" ? 200 : -10,
          collapsible: me.collapsibleConfig,
          items: {
            datePicker: {
              weekStartDay: me.weekStartDay
            }
          }
        }
      });
    } else {
      sidebar == null ? void 0 : sidebar.destroy();
      (_b2 = (_a5 = me.tbar) == null ? void 0 : _a5.widgetMap.toggleSidebar) == null ? void 0 : _b2.hide();
    }
  }
  changeTbar(tbar) {
    var _a5;
    if ((tbar == null ? void 0 : tbar.toggleSideBar) && !tbar.isWidget) {
      VersionHelper.deprecate("calendar", "7.0.0", "toggleSideBar ref is deprecated in favor of toggleSidebar");
      tbar.toggleSidebar = tbar.toggleSideBar;
      delete tbar.toggleSideBar;
    }
    const result = super.changeTbar(...arguments);
    if (result && !this.hasConfig("sidebar")) {
      (_a5 = result.widgetMap.toggleSidebar) == null ? void 0 : _a5.hide();
    }
    return result;
  }
  changeDatePicker(config) {
    if (config) {
      return Object.assign(config, {
        eventStore: this.eventStore,
        date: this.date,
        minDate: this.minDate,
        maxDate: this.maxDate
      });
    }
  }
  /**
   * A reference to the {@link Calendar.widget.CalendarDatePicker} which is shown in the {@link #property-sidebar} by default.
   *
   * This may be initially configured using the {@link #config-datePicker} config option.
   *
   * During the Calendar's lifecycle, this may be manipulated via its public API.
   * @member {Calendar.widget.CalendarDatePicker} datePicker
   * @readonly
   */
  get datePicker() {
    var _a5, _b2;
    return this.isConfiguring ? this._datePicker : (_b2 = (_a5 = this.sidebar) == null ? void 0 : _a5.widgetMap) == null ? void 0 : _b2.datePicker;
  }
  changeItems(items2) {
    const { modes, sidebar } = this;
    if (sidebar) {
      items2.sidebar = sidebar;
    }
    items2.viewContainer = this.viewContainer;
    items2.viewContainer.items = modes;
    items2.viewContainer.activeIndex = Math.max(Object.keys(modes).indexOf(this.mode), 0);
    if (this.activeModeIndex) {
      items2.viewContainer.layout.activeIndex = this.activeModeIndex;
    }
    const result = super.changeItems(items2);
    this._viewContainer = this.widgetMap.viewContainer;
    return result;
  }
  onViewCatchAll(event) {
    var _a5;
    const me = this, {
      fromOverflowPopup,
      type,
      source: view
    } = event;
    if (event.bubbles) {
      return;
    }
    let result, date2, { domEvent } = event;
    if (!domEvent && DomHelper.isDOMEvent(event.event)) {
      domEvent = event.event;
    }
    me.eventSource = view;
    if (!domEvent || me.handleEvent(domEvent) !== false) {
      const isEmptyCell = type.startsWith("emptycell"), dateContainmentFn = view.isEventList && !view.range ? "betweenLesserEqual" : "betweenLesser";
      if (type.endsWith("click") && !noDateChangeEvents[type] && (date2 = event.date || ((_a5 = view.getDateFromDomEvent) == null ? void 0 : _a5.call(view, domEvent)))) {
        if (view.isDayView && view.dayTime) {
          date2 = view.dayTime.startOfDay(date2);
        }
        const isOverflowClick = fromOverflowPopup || type === "celloverflowclick" || domEvent.target.closest(".b-cal-cell-overflow"), isOtherMonth = view.getDayElement && !view.getDayElement(date2, true) || view.isDateRangeOwner && !DateHelper[dateContainmentFn](date2, view.startDate, view.endDate), changeDate = view.syncCalendarDate && (view.isCalendarRow || !(fromOverflowPopup && isOtherMonth) && !isEmptyCell && !(view.isDateRangeOwner && isOtherMonth) && (type === "daynumberclick" || !(isOtherMonth && (isOverflowClick || type.startsWith("event") || type.startsWith("schedule")))));
        if (changeDate) {
          view.preventScroll = true;
          me.date = date2;
          view.preventScroll = false;
        }
      }
      if (isEmptyCell || relayedEvents.test(type)) {
        !event.view && (event.view = view);
        result = me.trigger(event.eventName, event);
      }
    }
    me.eventSource = null;
    return result;
  }
  // A mode had an internal change that requires updating the description shown in the tbar
  onViewDescriptionChange() {
    this.updateViewDescription();
  }
  // Implement drilling down from a MonthView week number click to a WeekView
  // or a child view which encapsulates WeekViews via its view.type config.
  onViewWeekNumberClick({ date: date2, week }) {
    const weekView = this.modes.week || this.viewContainer.query(isWeekView);
    if (weekView) {
      weekView.week = week;
      if (!DateHelper.betweenLesser(this.date, weekView.startDate, weekView.endDate)) {
        this.date = date2;
      }
      this.viewContainer.layout.activeItem = weekView;
    }
  }
  // Implement drilling down from a YearView month name click to the MonthView
  // or a child view which encapsulates MonthViews via its view.type config.
  onViewMonthNameClick({ date: date2 }) {
    const monthView = this.modes.month || this.viewContainer.query(isMonthView);
    if (monthView) {
      if (!monthView.getDayElement(date2, true)) {
        this.date = date2;
      }
      this.viewContainer.layout.activeItem = monthView;
    } else {
      this.date = date2;
    }
  }
  // Implement drilling down from a DayView's header day number click to the DayView
  // or a child view which encapsulates DayViews via its view.type config.
  onViewDayNumberClick({ source, date: date2, cellData, fromOverflowPopup, domEvent }) {
    var _a5;
    const { activeView } = this, validYearViewNavigate = source.isYearView && (source.overflowPopupTrigger !== "click" || fromOverflowPopup || !source.overflowPopup || cellData.isOtherMonth || !((_a5 = cellData.events) == null ? void 0 : _a5.length) && !source.emptyCellRenderer);
    if (activeView.isDayView && activeView.duration === 1) {
      return;
    }
    if (source.isCalendarRow || source.isMonthView || validYearViewNavigate || source.isAgendaView) {
      const { viewContainer } = this, dayView = this.modes.day || viewContainer.query((v) => {
        var _a6;
        return ((_a6 = v.view) == null ? void 0 : _a6.type.toLowerCase()) === "dayview";
      });
      if (dayView && source !== dayView.allDayEvents) {
        const { layout } = viewContainer, { activeItem: oldActiveItem } = layout, { syncViewDate } = oldActiveItem;
        oldActiveItem.syncViewDate = false;
        this.date = date2;
        viewContainer.layout.activeItem = dayView;
        oldActiveItem.syncViewDate = syncViewDate;
        return false;
      }
    }
  }
  onViewBeforeEventAdd({ eventRecord, assignmentRecords }) {
    const { eventEdit } = this.features, isCreating = eventEdit && !eventEdit.disabled;
    eventRecord.isCreating = isCreating;
    assignmentRecords == null ? void 0 : assignmentRecords.forEach((assignmentRecord) => assignmentRecord.isCreating = isCreating);
  }
  /**
   * Uses the current active mode to create an event on the specified date which conforms to that mode's
   * {@link Calendar.widget.mixin.CalendarMixin#config-autoCreate} setting.
   *
   * This method may be called programmatically by application code if the `autoCreate` setting
   * is `false`, in which case the default values for `autoCreate` will be used.
   *
   * If the {@link Calendar.feature.EventEdit EventEdit} feature is active, the new event
   * will be displayed in the event editor.
   *
   * This is a utility method to use as a shortcut to {@link Calendar.widget.mixin.CalendarMixin#function-createEvent}
   * @param {Date} [date] Optionally, the date to add the event at. Uses the Calendar's currently
   * active date, as set in the {@link #config-sidebar}'s date picker.
   *
   * If there's no time component, and the current active mode is a calendar view, the
   * {@link Calendar.widget.mixin.CalendarMixin#config-autoCreate}'s `startHour` will be used.
   */
  createEvent(date2, resourceRecord) {
    this.doCreateEvent(date2, resourceRecord);
  }
  // This is separate because its signature is fixed. Second parameter is an optional view to use
  // to do the creating. The createEvent method may be called by Features which pass extra parameters.
  doCreateEvent(date2, resourceRecord, source = this.activeSubView, isAutoCreate) {
    const { modes } = this, editingView = source.isYearView ? modes.week || modes.month || modes.day || source : source;
    if (!this.activeView.owns(editingView)) {
      const { viewContainer } = this.widgetMap, { layout } = viewContainer;
      if (editingView) {
        viewContainer.ion({
          activeItemChange: "doCreateEvent",
          thisObj: source,
          once: true,
          args: [date2, resourceRecord, editingView]
        });
        if (date2) {
          this.date = date2;
        }
        layout.activeItem = editingView;
        return false;
      }
    } else {
      source.doCreateEvent ? source.doCreateEvent(date2, resourceRecord, editingView, isAutoCreate) : source.createEvent(date2, resourceRecord, isAutoCreate);
    }
  }
  changeDate(date2, oldDate) {
    date2 = typeof date2 === "string" ? DateHelper.parse(date2) : new Date(date2);
    if (isNaN(date2)) {
      throw new Error("Calendar widget date ingestion must be passed a Date, or a YYYY-MM-DD date string");
    }
    date2 = DateHelper.clearTime(date2);
    if (!oldDate || date2 - oldDate) {
      return date2;
    }
  }
  changeMinDate(minDate) {
    return this.changeDate(minDate);
  }
  changeMaxDate(maxDate) {
    return this.changeDate(maxDate);
  }
  updateMinDate(minDate) {
    if (!this.isConfiguring) {
      this.syncCommonConfig("minDate");
      this.datePicker && (this.datePicker.minDate = minDate);
    }
  }
  updateMaxDate(maxDate) {
    if (!this.isConfiguring) {
      this.syncCommonConfig("maxDate");
      this.datePicker && (this.datePicker.maxDate = maxDate);
    }
  }
  updateAutoCreate() {
    if (!this.isConfiguring) {
      this.syncCommonConfig("autoCreate");
    }
  }
  updateDate(date2, oldDate) {
    if (!this.isConfiguring) {
      const { sidebar } = this;
      if (sidebar) {
        sidebar.date = date2;
      } else {
        this.onCalendarDateChange({ date: date2, oldDate });
      }
    }
  }
  updateDateFormat() {
    if (!this.isConfiguring) {
      this.syncCommonConfig("dateFormat");
    }
  }
  changeIncludeWeekendsButton(value2) {
    return value2 === true ? {} : value2;
  }
  updateIncludeWeekendsButton(value2) {
    if (!this.isConstructing && value2 && ObjectHelper.isObject(value2)) {
      this.widgetMap.modeSelector.includeWeekendsButton = value2;
    }
  }
  changeScrollManager(scrollManager, oldScrollManager) {
    oldScrollManager == null ? void 0 : oldScrollManager.destroy();
    if (this.isDestroying) {
      return;
    }
    return new ScrollManager(Object.assign({
      element: this.element
    }, scrollManager));
  }
  updateDefaultCalendar() {
    if (!this.isConfiguring) {
      this.syncCommonConfig("defaultCalendar");
    }
  }
  changeWeekStartDay(weekStartDay) {
    if (weekStartDay === false) {
      weekStartDay = DateHelper.weekStartDay;
    }
    return weekStartDay;
  }
  updateReadOnly() {
    super.updateReadOnly(...arguments);
    if (!this.isConfiguring) {
      this.syncCommonConfig("readOnly");
    }
  }
  updateWeekStartDay(weekStartDay) {
    var _a5;
    if (!this.isConfiguring) {
      this.syncCommonConfig("weekStartDay");
      if ((_a5 = this.sidebar) == null ? void 0 : _a5.widgetMap.datePicker) {
        this.sidebar.widgetMap.datePicker.weekStartDay = weekStartDay;
      }
    }
  }
  changeHideNonWorkingDays(hideNonWorkingDays) {
    return Boolean(hideNonWorkingDays);
  }
  updateHideNonWorkingDays(hideNonWorkingDays, was) {
    if (!this.isConfiguring) {
      this.syncCommonConfig("hideNonWorkingDays");
      this.trigger("changeHideNonWorkingDays", {
        value: hideNonWorkingDays,
        was
      });
    }
  }
  changeNonWorkingDays(nonWorkingDays) {
    if (nonWorkingDays === false) {
      nonWorkingDays = DateHelper.nonWorkingDays;
    }
    const me = this, result = new Proxy({ ...nonWorkingDays }, {
      set(target) {
        const result2 = Reflect.set(...arguments);
        me.updateNonWorkingDays(target);
        return result2;
      },
      deleteProperty(target) {
        const result2 = Reflect.deleteProperty(...arguments);
        me.updateNonWorkingDays(target);
        return result2;
      }
    });
    return result;
  }
  updateNonWorkingDays() {
    if (!this.isConfiguring) {
      this.syncCommonConfig("nonWorkingDays");
    }
  }
  updateCoreHours() {
    if (!this.isConfiguring) {
      this.syncCommonConfig("coreHours");
    }
  }
  /**
   * Syncs configs which are passed down into child views when changed here.
   * @param {String} name The name of the config to pass from this Calendar into every child view
   * @private
   */
  syncCommonConfig(name) {
    this.eachView((v) => {
      v[name] = this[name];
    });
  }
  onCalendarStoreChange() {
    var _a5;
    if (this.isPainted) {
      const {
        element,
        resourceStore
      } = this, { resourceFilter } = this.widgetMap, visibleResourceCount = (resourceFilter ? resourceStore.records.filter((c) => resourceFilter.value.includes(c)) : resourceStore.records).length;
      element.classList.toggle("b-no-visible-resources", Boolean(resourceFilter && !visibleResourceCount));
      element.style.setProperty("--visible-resource-count", visibleResourceCount);
    }
    (_a5 = super.onCalendarStoreChange) == null ? void 0 : _a5.call(this, ...arguments);
  }
  updateSelected() {
    const eventEls = this.viewContainer.contentElement.querySelectorAll(".b-cal-event-wrap");
    for (let i = 0, { length } = eventEls; i < length; i++) {
      eventEls[i].classList.toggle(this.eventSelectedCls, this.isEventSelected(eventEls[i].dataset.eventId));
    }
  }
  getElementFromEventRecord(eventRecord, resourceRecord) {
    return this.activeView.getEventElement(eventRecord, void 0, resourceRecord);
  }
  getElementsFromEventRecord(eventRecord, resourceRecord) {
    return [this.getElementFromEventRecord(eventRecord, resourceRecord)];
  }
  updateLocalization() {
    const me = this;
    if (me.isPainted) {
      let refreshed = false;
      if (!("weekStartDay" in me.initialConfig) && me.weekStartDay !== DateHelper.weekStartDay) {
        me.weekStartDay = DateHelper.weekStartDay;
        refreshed = true;
      }
      if (!("nonWorkingDays" in me.initialConfig) && !ObjectHelper.isEqual(me.nonWorkingDays, DateHelper.nonWorkingDays)) {
        me.nonWorkingDays = DateHelper.nonWorkingDays;
        refreshed = true;
      }
      if (!refreshed) {
        me.refresh();
      }
      me.syncUIWithActiveView();
    }
    super.updateLocalization();
  }
  /**
   * A method which may be chained by features. It is called when an event's render
   * data is calculated so that features may update the style, class list or body.
   * The {@link Core.helper.DomSync} DomConfig object which will be used to create the
   * event bar is passed, so the content may be manipulated.
   * @param {Object} renderData Context for the event bar config creation.
   * @param {DomConfig} eventDomConfig A {@link Core.helper.DomSync} DomConfig object which describes the
   * structure of the event to be rendered.
   * @internal
   */
  onEventDataGenerated(renderData, eventDomConfig) {
  }
};
//region Config
__publicField(_Calendar, "$name", "Calendar");
// Factoryable type name
__publicField(_Calendar, "type", "calendar");
var Calendar = _Calendar;
Calendar.initClass();
if (globalThis.bryntum) {
  globalThis.bryntum.Calendar = Calendar;
}
var Modes = class extends Base.mixin(Factoryable_default) {
  static get factoryable() {
    return {
      // Allow any widget type:
      extends: Widget
    };
  }
};
Modes.register("agenda", AgendaView);
Modes.register("year", YearView);
Modes.register("month", MonthView);
Modes.register("week", WeekView);
Modes.register("day", DayView);
Modes.register("dayagenda", DayAgendaView);
Modes.register("list", EventList);
Modes.register("resource", ResourceView);
Modes.register("dayresource", DayResourceView);
Calendar.Modes = Modes;
VersionHelper.setVersion("calendar", "6.2.5");
Calendar._$name = "Calendar";

// lib/Calendar/widget/EventTip.js
var hasEventStore5 = (w) => w.eventStore;
var EventTip = class extends Tooltip {
  constructor() {
    super(...arguments);
    __publicField(this, "clockTemplate", new ClockTemplate({}));
  }
  static get configurable() {
    return {
      localizableProperties: ["timeFormat"],
      align: {
        // Needed because we show on click meaning the tip persists and may need
        // to adjust to UI shape changes.
        monitorResize: true
      },
      /**
       * A {@link Core.helper.DateHelper} format string used to format dates displayed in this tooltip.
       *
       * @config {String}
       * @default
       */
      dateFormat: "ll",
      /**
       * The event which the tooltip feature has been shown for.
       * @member {Scheduler.model.EventModel} eventRecord
       * @readonly
       */
      /**
       * The event record for this tip to display
       * @config {Scheduler.model.EventModel} eventRecord
       */
      eventRecord: {
        $config: "nullify",
        // ensure we remove our afterChange hook
        value: null
      },
      recurrenceHint: "",
      /**
       * A {@link Core.helper.DateHelper} format string used to format the time displayed in this tooltip.
       *
       * @config {String}
       * @default 'LST'
       */
      timeFormat: {
        value: "LST",
        $config: {
          localeKey: "L{timeFormat}"
        }
      },
      // Allow it to fall back from its initial alignment axis to the cross axis.
      // So if aligned l-r or r-l and shown by a long multiday event, it allows
      // itself to be fall back to being aligned t-b
      axisLock: "flexible",
      closable: null,
      forSelector: ".b-cal-event-wrap",
      maxWidth: "30em",
      minWidth: "14em",
      scrollAction: "realign",
      cls: {
        "b-sch-event-tooltip": 1
      },
      tools: {
        edit: {
          cls: "b-icon-edit",
          handler: "up.onEditClick",
          tooltip: "L{EventEdit.Edit event}",
          weight: 100
        },
        delete: {
          cls: "b-icon-trash",
          handler: "up.onDeleteClick",
          tooltip: "L{SchedulerBase.Delete event}",
          weight: 0
        },
        maximize: null
      },
      /**
       * A function, or name of a function in the ownership hierarchy which is used to create
       * the content of the tooltip's header.
       *
       * @prp {Function|String}
       * @param {Scheduler.model.EventModel} eventRecord The event record
       * @returns {String|DomConfig} The content of the header element.
       */
      titleRenderer: (eventRecord) => StringHelper.encodeHtml(eventRecord.name),
      /**
       * A function, or name of a function in the ownership hierarchy which is used to create
       * the content of the tooltip's body.
       *
       * @prp {Function|String}
       * @param {Object} data Contextual data about the current tooltip activation
       * @param {HTMLElement} data.activeTarget The event bar element being aligned to
       * @param {Scheduler.model.EventModel} data.eventRecord The event record
       * @param {Event} data.event The DOM event which initiated the tooltip show
       * @param {Calendar.widget.EventTip} data.tip The tooltip instance being shown
       * @returns {String|DomConfig} The content of the body element.
       */
      renderer: null,
      /**
       * By default, the end date of an all day event is displayed in the tooltip UI as
       * the last calendar date on which the event falls. For most end users, this is the
       * expected value.
       *
       * Technically, the {@link Scheduler.model.EventModel#field-endDate} is a timestamp
       * which represents the exact point in time at which an event ends. To use this instead,
       * configure `extendAllDayEndDay` as `true`.
       *
       * To be clear, this would mean that an {@link Scheduler.model.EventModel#field-allDay}
       * event starting and ending on the 7th of February 2020, would show the end date in the
       * tooltip as 8th of February 2020.
       * @prp {Boolean}
       * @default false
       */
      extendAllDayEndDay: null,
      activeClient: null,
      /**
       * By default, if the tip's target event is in a cluster of overlapping events and therefore
       * narrow, activating the tip will expand it to full width temporarily.
       *
       * Configure this as `false` to disable this.
       * @prp {Boolean}
       * @default true
       */
      revealEventsInCluster: true
    };
  }
  static get delayable() {
    return {
      refreshContent: 20
    };
  }
  //endregion
  get owner() {
    return Tooltip.fromElement(this.activeTarget) || this.ownerFeature.client;
  }
  onEditClick() {
    this.trigger("edit");
  }
  onDeleteClick() {
    this.trigger("delete");
  }
  getHtml(data) {
    const me = this, { eventRecord } = me;
    if (eventRecord) {
      data.eventRecord = eventRecord;
      me.title = me.titleRenderer ? me.callback(me.titleRenderer, me, [eventRecord]) : "";
      return me.callback(me.renderer || me.internalRenderer, me, [{
        ...data,
        eventRecord
      }]);
    }
  }
  internalRenderer({ eventRecord }) {
    var _a5;
    const {
      clockTemplate,
      recurrenceHint
    } = this, {
      allDay,
      recurrence
    } = eventRecord, activeClient = ((_a5 = Tooltip.fromElement(this.activeTarget)) == null ? void 0 : _a5.closest(hasEventStore5)) || this.owner.activeView, recurrenceLegend = recurrence ? RecurrenceLegend.getLegend(recurrence) : recurrenceHint, duration = DateHelper.formatDelta(DateHelper.diff(eventRecord.startDate, eventRecord.endDate), allDay ? { precision: "d" } : null), {
      multiDay,
      startDate,
      startText,
      endDate,
      endText
    } = Calendar.getStartAndEndDates(activeClient, eventRecord, this.dateFormat, this.timeFormat, this.extendAllDayEndDay);
    clockTemplate.mode = multiDay ? "day" : "hour";
    return clockTemplate.template({
      date: startDate,
      text: startText,
      cls: "b-sch-tooltip-startdate"
    }) + (endText ? clockTemplate.template({
      date: endDate,
      text: endText,
      cls: "b-sch-tooltip-enddate"
    }) : "") + (eventRecord.isMilestone ? "" : `<div class="b-cal-tooltip-duration b-icon b-icon-duration">${duration}</div>`) + (recurrenceLegend ? `<div class="b-cal-tooltip-recurrence b-icon b-icon-recurring">${recurrenceLegend}</div>` : "");
  }
  showByEvent(event, element = event.target) {
    var _a5, _b2;
    const me = this, clickDelay = (_a5 = me.ownerFeature) == null ? void 0 : _a5.clickDelay;
    me.activeTarget = element;
    me.pointerEvent = event;
    me.updateContent();
    if ((_b2 = me.ownerFeature) == null ? void 0 : _b2.alignToDomEvent) {
      event.target.dataset.eventId = element.dataset.eventId;
      element = {
        domEvent: event,
        anchor: false,
        align: "s0-e100",
        axisLock: true
      };
    }
    if (event.type === "click" && clickDelay) {
      if (me.hasTimeout("show")) {
        me.clearTimeout("show");
      } else {
        me.hoverDelay = clickDelay;
        event.type = "pointerover";
        me.delayShow(element);
      }
      return;
    }
    return me.showBy(element);
  }
  afterShowByTarget() {
    var _a5;
    const { delete: deleteTool } = this.tools;
    this.activeClient = (_a5 = Tooltip.fromElement(this.activeTarget)) == null ? void 0 : _a5.closest(hasEventStore5);
    super.afterShowByTarget();
    deleteTool && (deleteTool.disabled = this.ownerFeature.client.readOnly);
  }
  afterHide() {
    super.afterHide(...arguments);
    this.activeClient = null;
  }
  updateActiveTarget(element, was) {
    var _a5;
    (_a5 = super.updateActiveTarget) == null ? void 0 : _a5.call(this, ...arguments);
    was == null ? void 0 : was.classList.remove("b-cal-event-reveal");
    if (element) {
      if (element.classList.contains("b-cal-in-cluster") && this.revealEventsInCluster) {
        element.classList.add("b-cal-event-reveal");
      }
    } else {
      this.eventRecord = null;
    }
  }
  refreshContent() {
    this.eventRecord && this.updateContent();
  }
  updateEventRecord(eventRecord) {
    var _a5;
    const me = this;
    (_a5 = me._changeHook) == null ? void 0 : _a5.call(me);
    me._changeHook = eventRecord && FunctionHelper.after(eventRecord, "afterChange", me.refreshContent, me);
    if (eventRecord) {
      if (me.tools.delete) {
        me.tools.delete.hidden = eventRecord.readOnly;
      }
      if (me.tools.edit) {
        me.tools.edit.hidden = eventRecord.readOnly;
      }
    }
  }
  updateContent() {
    var _a5;
    const { clockTemplate, element, eventRecord } = this, result = super.updateContent(), startElement = element.querySelector(".b-sch-tooltip-startdate"), endElement = element.querySelector(".b-sch-tooltip-enddate"), { edit, delete: del } = this.tools, { client } = this.ownerFeature, { eventEdit } = client.features, readOnly = client.readOnly || eventRecord.readOnly;
    startElement && clockTemplate.updateDateIndicator(startElement, eventRecord.startDate);
    endElement && clockTemplate.updateDateIndicator(endElement, eventRecord.endDate);
    edit && (edit.hidden = readOnly || !(eventEdit == null ? void 0 : eventEdit.enabled));
    del && (del.hidden = readOnly);
    if (this.resources) {
      const { chipView } = this;
      chipView.items = eventRecord.resources;
      (_a5 = this.contentElement.querySelector(".b-resourcechipview")) == null ? void 0 : _a5.remove();
      this.contentElement.appendChild(chipView.element.cloneNode(true));
    }
    return result;
  }
  realign() {
    var _a5;
    const { lastAlignSpec } = this;
    if (this.isVisible && (lastAlignSpec == null ? void 0 : lastAlignSpec.aligningToElement) && lastAlignSpec.target.dataset.eventId !== String((_a5 = this.eventRecord) == null ? void 0 : _a5.id)) {
      this.hide();
    }
    super.realign(...arguments);
  }
  updateActiveClient(activeClient) {
    this.detachListeners("clientOverflowChange");
    if (activeClient) {
      activeClient.ion({
        name: "clientOverflowChange",
        eventsPerCellChange: "onClientOverflowChange",
        thisObj: this
      });
    }
  }
  onClientOverflowChange() {
    if (!DomHelper.isVisible(this.lastAlignSpec.target)) {
      this.hide();
    }
  }
  onDocumentMouseDown({ event }) {
    if (!event.button && this.ownerFeature.showOn === "click" && this.activeTarget.contains(event.target)) {
      return;
    }
    return super.onDocumentMouseDown(...arguments);
  }
  get chipView() {
    const me = this;
    if (!me._chipView) {
      me._chipView = new ResourceChipView({
        parent: me
      });
      Object.defineProperty(me.chipView, "isVisible", {
        get() {
          return true;
        }
      });
      me.chipView.triggerPaint();
    }
    return me._chipView;
  }
};
//region Config
__publicField(EventTip, "$name", "EventTip");
__publicField(EventTip, "type", "eventtip");
EventTip.initClass();
EventTip._$name = "EventTip";

// lib/Calendar/feature/CalendarDrag.js
var tentativeCls = "b-cal-tentative-event";
var SECONDS = 1e3;
var MINUTES = 60 * SECONDS;
var YYYY_MM_DD = "YYYY-MM-DD";
var edgeRe = /^b-hover-(top|right|bottom|left)$/;
var eventDragSym = Symbol("eventDragMode");
var appendEventFooter = (domConfig, footer) => {
  let ret;
  if (domConfig.className === "b-cal-event-body") {
    domConfig.children.push(ret = footer);
  } else if (Array.isArray(domConfig.children)) {
    domConfig.children.forEach((c) => {
      if (!ret && c) {
        ret = appendEventFooter(c, footer);
      }
    });
  }
  return ret;
};
var makeMode = (type) => Object.freeze({
  type,
  create: false,
  move: false,
  resize: false,
  [type]: true
});
var modeDescriptor = {
  create: {
    finisher: "finishDropCreate",
    mode: makeMode("create"),
    validatorFn: "validateCreateFn"
  },
  move: {
    finisher: "finishDropMove",
    mode: makeMode("move"),
    validatorFn: "validateMoveFn"
  },
  resize: {
    finisher: "finishDropResize",
    mode: makeMode("resize"),
    validatorFn: "validateResizeFn"
  }
};
var isAllDayView = {
  calendarrow: true,
  monthview: true,
  dayresourcecalendarrow: true
};
var metaProp = Symbol("meta");
var Zone = class extends CalendarZone.mixin(Draggable_default, Droppable_default, Hoverable_default) {
  static get configurable() {
    return {
      // We limit dropping to all *children* of the Droppable, not the Droppable element itself.
      // So that we avoid triggering "over" or "drop" on borders which would give incorrect
      // positional calculations.
      droppableSelector: "*",
      droppable: true,
      hoverable: null,
      hoverAnimationCls: "b-hover-anim",
      days: {
        $config: {
          // DayResourceView includes the resource as part of the days config.
          equal: (d1, d2) => ObjectHelper.isEqual(d1, d2) && (d1 == null ? void 0 : d1.resource) === (d2 == null ? void 0 : d2.resource)
        },
        value: null
      },
      dragProxy: {
        type: "tip",
        tooltip: null
        // borrowed from the Feature instance's tooltip config
      },
      eventRecord: null,
      hit: null,
      dragItemSelector: ".b-cal-event-wrap",
      overflow: {
        $config: "nullify",
        value: null
      },
      rootElement: null
    };
  }
  get dayTime() {
    var _a5;
    return (_a5 = this.view) == null ? void 0 : _a5.dayTime;
  }
  clearTime(date2) {
    return this.dayTime.startOfDay(date2);
  }
  findRootElement(view) {
    return view.contentElement;
  }
  getDateFromPosition(clientX, clientY) {
    return this.view.getDateFromPosition(clientX, clientY, false, this.view.dayTime);
  }
  // Draggable behaviors
  get dragEventer() {
    return this.owner;
  }
  beforeDrag(drag) {
    const me = this, { owner } = me, hit = me.hitTest(drag), eventRecord = hit == null ? void 0 : hit.eventRecord, insetProp = me.view.rtl ? "right" : "left";
    if (!hit || (eventRecord == null ? void 0 : eventRecord.readOnly) || (eventRecord == null ? void 0 : eventRecord.isCreating)) {
      return false;
    }
    hit.date && drag.set("date", hit.date);
    hit.resource && drag.set("resource", hit.resource);
    let mode, veto;
    switch (hit.type) {
      case "event":
        drag.set("eventRecord", eventRecord);
        drag.set("eventSourceHit", hit);
        drag.set("eventInset", hit.eventElement.style[insetProp]);
        drag.set("eventWidth", hit.eventElement.offsetWidth);
        mode = "move";
        veto = !owner.draggable || !eventRecord.isDraggable || me.draggable === false;
        if (!veto) {
          me.captureDragOffset(eventRecord, hit, drag);
        }
        break;
      case "resize":
        drag.set("eventRecord", eventRecord);
        drag.set("eventSourceHit", hit);
        drag.set("eventInset", hit.eventWrap.style[insetProp]);
        drag.set("eventWidth", hit.eventWrap.offsetWidth);
        mode = "resize";
        veto = !owner.resizable || !eventRecord.resizable || me.resizable === false;
        break;
      case "schedule":
      case "dayNumber":
        drag.set("eventCreate", hit);
        mode = "create";
        veto = !owner.creatable || me.creatable === false;
        break;
      default:
        return false;
    }
    if (veto) {
      return false;
    }
    if (me.view[`allowDrag${StringHelper.capitalize(mode)}`] === false) {
      return false;
    }
    drag.set("eventDragMode", drag[eventDragSym] = modeDescriptor[mode].mode);
  }
  dragStart() {
    var _a5, _b2;
    const me = this, { dragging: drag, owner, view } = me, { client } = owner, callback = () => {
      drag.lastMoveEvent && drag.move(drag.lastMoveEvent);
    }, config = {
      scrollables: client.scrollManager ? [
        {
          element: client.viewContainer.element,
          callback
        }
      ] : []
    };
    if (me.isResizing || me.isMoving) {
      me.refocus = owner.client.captureFocus();
    }
    if (me.isResizing) {
      (_a5 = drag.itemElement) == null ? void 0 : _a5.classList.add("b-resizing");
    }
    if (view.scrollable) {
      config.scrollables.push({
        element: view.scrollable.element,
        callback
      });
    }
    if (view.horizontalScroller) {
      config.scrollables.push({
        element: view.horizontalScroller.element,
        callback
      });
    }
    (_b2 = client.scrollManager) == null ? void 0 : _b2.startMonitoring(config);
  }
  captureDragOffset() {
  }
  cleanupDrag() {
    var _a5;
    const me = this, { dragProxy, dragging } = me, { tooltip } = dragProxy, view = me.view.isOverflowPopup ? me.view.owner : me.view, { client } = me.owner, activeElement = DomHelper.getActiveElement(client);
    (_a5 = dragging == null ? void 0 : dragging.itemElement) == null ? void 0 : _a5.classList.remove("b-resizing");
    if (dragging == null ? void 0 : dragging.aborted) {
      me.days = null;
    }
    view.afterRefresh(() => {
      var _a6;
      me.eventRecord && (me.days = null);
      if (DomHelper.getActiveElement(client) === activeElement) {
        (_a6 = me.refocus) == null ? void 0 : _a6.call(me, false, true);
      }
      me.refocus = null;
    });
    if (tooltip) {
      tooltip.hide();
      dragProxy.tooltip = null;
    }
  }
  dragEnd() {
    var _a5, _b2;
    (_b2 = (_a5 = this.owner.client) == null ? void 0 : _a5._scrollManager) == null ? void 0 : _b2.stopMonitoring();
    this.cleanupDrag();
  }
  makeDays(startDate, endDate) {
    const { dayTime, firstVisibleDate, lastVisibleDate } = this.view, days2 = [];
    endDate = DateHelper.add(endDate, -1, "d");
    [startDate, endDate] = [startDate, endDate].map(
      (d) => d < firstVisibleDate ? firstVisibleDate : lastVisibleDate < d ? lastVisibleDate : d
    );
    let date2 = dayTime.startOfDay(startDate);
    for (const end = dayTime.ceil(endDate); !(end < date2); date2 = DateHelper.add(date2, 1, "day")) {
      days2.push(dayTime.dateKey(date2));
    }
    return days2;
  }
  onShowOverflowPopup({ overflowPopup }) {
    if (!this.overflow) {
      this.overflow = new OverflowZone({
        owner: this.owner,
        view: overflowPopup
      });
    }
  }
  // Droppable behaviors
  get calendarCellSelector() {
    return this.view.visibleCellSelector;
  }
  get calendarCells() {
    const container = this.dropRootElement;
    return container && DomHelper.children(container, this.calendarCellSelector);
  }
  get isCreating() {
    var _a5, _b2;
    return (_b2 = (_a5 = this.dropping) == null ? void 0 : _a5[eventDragSym]) == null ? void 0 : _b2.create;
  }
  get isMoving() {
    var _a5, _b2, _c2;
    const mode = ((_a5 = this.dropping) == null ? void 0 : _a5[eventDragSym]) || ((_b2 = this.dragging) == null ? void 0 : _b2[eventDragSym]);
    return mode ? mode.move : Boolean((_c2 = this.dropping) == null ? void 0 : _c2.peek("eventRecord"));
  }
  /**
   * Returns true if a resize operation is active
   * @property {Boolean}
   * @readonly
   */
  get isResizing() {
    var _a5, _b2;
    return (_b2 = (_a5 = this.dragging) == null ? void 0 : _a5[eventDragSym]) == null ? void 0 : _b2.resize;
  }
  get recurrable() {
    return this.isDayZone || this.isMonthZone;
  }
  cleanupDrop() {
    this.eventRecord = this.eventDom = this.days = null;
    this.noTip = false;
  }
  createEvent(data, dropping = this.dropping) {
    const mode = (dropping == null ? void 0 : dropping[eventDragSym]) || modeDescriptor.create.mode;
    this.setupEvent(data, mode.create);
  }
  async dragDrop(drag) {
    if (this.isResizing || drag.target === this) {
      return drag.finalizer = this.finishDrop(drag);
    }
  }
  dragEnter(drag) {
    const me = this, mode = drag[eventDragSym], hit = drag.source === me && drag.peek("eventSourceHit") || me.hitTest(drag);
    if (me.isMoving) {
      me._hit = hit;
      me.startMove(drag.peek("eventRecord"));
    } else if (!mode || drag.source !== me) {
      return false;
    } else if (mode.resize) {
      me._hit = hit;
      me.startResize(hit);
    } else if (mode.create) {
      me._hit = hit;
      me.startCreate(drag.peek("date"), drag.peek("eventCreate"));
    } else {
      return false;
    }
  }
  dragLeave() {
    if (!this.isResizing) {
      this.cleanupDrop();
    }
  }
  dragMove(drag) {
    this.hit = this.pickDropTarget(drag);
  }
  dropHitMove(drag, hit, eventRecord) {
    const me = this, { endDate, startDate } = eventRecord.isScheduled ? eventRecord : me.eventRecord, durationSec = DateHelper.diff(startDate, endDate, "s");
    let { date: date2, resource } = hit;
    date2 = me.clearTime(date2);
    if (!eventRecord.allDay) {
      date2 = DateHelper.add(date2, DateHelper.diff(me.clearTime(startDate), startDate, "s"), "s");
    }
    date2 = me.applyDragOffset(date2, drag);
    const data = {
      startDate: date2,
      endDate: DateHelper.add(date2, durationSec, "s")
    };
    if (resource == null ? void 0 : resource.eventColor) {
      data.eventColor = resource.eventColor;
    }
    me.setEventData(data);
  }
  dropHitNowhere() {
    this.days = null;
  }
  dropHitResize(drag, hit, eventHit) {
    const me = this, { eventRecord } = me, { startDate, endDate } = eventRecord, date2 = me.clearTime(hit.date);
    let changes, end;
    if (eventHit.atEnd) {
      end = DateHelper.add(date2, 1, "d");
      changes = {
        startDate,
        endDate: startDate < end ? end : DateHelper.add(startDate, 1, "d")
      };
    } else {
      changes = {
        startDate: date2 < endDate ? date2 : DateHelper.add(endDate, -1, "d"),
        endDate
      };
    }
    changes.duration = DateHelper.diff(changes.startDate, changes.endDate, eventRecord.durationUnit);
    me.setEventData(changes);
  }
  async finishDrop(drag) {
    const me = this, {
      eventRecord,
      owner,
      view
    } = me, { eventStore } = view, mode = drag[eventDragSym], descriptor = modeDescriptor[(mode == null ? void 0 : mode.type) || "move"];
    if (descriptor && me.hit) {
      const validation = descriptor.validatorFn ? await owner.callback(owner[descriptor.validatorFn], owner, [{
        drag,
        eventRecord,
        event: drag.event
      }]) : true;
      view.eventContentElement.querySelectorAll(".b-cal-tentative-event").forEach((e) => e.retainElement = false);
      if (validation !== false) {
        await me[descriptor.finisher](
          drag,
          owner,
          eventRecord,
          eventStore,
          validation,
          async (eventName, callback, callbackFalse) => {
            let info = {
              drag,
              eventRecord,
              newStartDate: eventRecord.startDate,
              newEndDate: eventRecord.endDate,
              resourceRecord: me.hit.resource || view.resource,
              validation,
              event: drag.event,
              feature: owner,
              view: drag[me.isResizing ? "source" : "target"].view
            };
            const result = await owner.client.trigger("before" + StringHelper.capitalize(eventName), info);
            if (result === false) {
              info = false;
              await (callbackFalse == null ? void 0 : callbackFalse());
            } else {
              view.allowTransitionsOnRefresh = false;
              await (callback == null ? void 0 : callback());
              delete info.newStartDate;
              delete info.newEndDate;
              if ((mode == null ? void 0 : mode.type) !== "create") {
                info.eventRecord = drag.peek("eventRecord") || eventRecord;
              }
              owner.client.trigger(eventName, info);
            }
            return info;
          }
        );
      }
    }
    me.isFinishing = true;
    me.cleanupDrop();
  }
  async finishDropCreate(drag, owner, eventRecord, eventStore, validation, triggerFn) {
    const add = (validation == null ? void 0 : validation.add) !== false;
    const result = await triggerFn("dragCreateEnd");
    if (result === false) {
      if (add) {
        eventStore.remove(eventRecord);
      }
    } else if (add && !eventStore.includes(eventRecord)) {
      eventStore.add(eventRecord);
    }
  }
  async finishDropMove(drag, owner, eventRecord, eventStore, validation, triggerFn) {
    var _a5;
    let dropRec = await drag.get("eventRecord");
    const me = this, { view } = me, storeRec = dropRec.isOccurrence ? dropRec.recurringTimeSpan : dropRec, { source } = drag, fromResource = await drag.get("resource"), toResource = me.hit.resource, interView = source !== me, sourceStore = interView && ((_a5 = source.view) == null ? void 0 : _a5.eventStore) && view.eventStore && source.view.eventStore !== view.eventStore ? source.view.eventStore : await drag.get("sourceStore"), isReassign = toResource && toResource !== fromResource;
    if (!isReassign && drag.source === drag.target && DateHelper.isEqual(eventRecord.startDate, dropRec.startDate) && DateHelper.isEqual(eventRecord.endDate, dropRec.endDate)) {
      if (dropRec.eventStore === eventStore) {
        return;
      }
    }
    await triggerFn("dragMoveEnd", async () => {
      if (!eventStore.getByInternalId(storeRec.internalId)) {
        if (sourceStore) {
          if (me.owner.removeFromExternalStore) {
            sourceStore.remove(dropRec);
          } else {
            dropRec = dropRec.copy();
          }
        } else {
          dropRec = eventRecord;
        }
        dropRec.setData("resourceId", null);
        eventStore.add(dropRec);
        await me.moveEventTo(drag, dropRec);
        const resource = toResource != null ? toResource : !isReassign && !view.project.resourceStore.includes(dropRec.resource) && me.owner.client.defaultCalendarId;
        if (resource != null && view.project.resourceStore.includes(resource)) {
          dropRec.assign(resource);
        } else if (!view.project.resourceStore.includes(dropRec.resource)) {
          dropRec.resources = [];
        }
      } else {
        dropRec = await me.finishDropConfirm(dropRec);
        if (dropRec) {
          await me.moveEventTo(drag, dropRec);
          if (isReassign) {
            if (dropRec.isOccurrence) {
              dropRec.convertToRealEvent();
            }
            dropRec.assign(toResource);
            dropRec.unassign(fromResource);
          }
        }
      }
      await view.project.commitAsync();
    });
  }
  async finishDropResize(drag, owner, eventRecord, eventStore, validation, triggerFn) {
    const me = this, { view } = me, {
      eventRecord: tempEventRecord,
      edge
    } = await drag.get("eventSourceHit"), prop = edge.bottom || (view.rtl ? edge.left : edge.right) ? "endDate" : "startDate";
    if (!(eventRecord[prop] - tempEventRecord[prop])) {
      return;
    }
    await triggerFn("dragResizeEnd", async () => {
      const editRec = await this.finishDropConfirm(tempEventRecord);
      editRec && editRec.set({
        startDate: eventRecord.startDate,
        endDate: eventRecord.endDate,
        duration: DateHelper.diff(eventRecord.startDate, eventRecord.endDate, editRec.durationUnit)
      });
      await view.project.commitAsync();
    });
  }
  async finishDropConfirm(eventRecord) {
    if (eventRecord.isOccurrence || eventRecord.isRecurring) {
      return new Promise((resolve) => {
        const dialog = this.owner.client.recurrenceConfirmationPopup;
        dialog.owner = this.owner;
        dialog.confirm({
          actionType: "update",
          eventRecord,
          cancelFn() {
            resolve(null);
          },
          changerFn(eventRec) {
            resolve(eventRec);
          }
        });
      });
    }
    return eventRecord;
  }
  async moveEventTo(drag, eventRecord) {
    const me = this;
    let date2 = me.view.getDateFromElement(drag.targetElement);
    if (eventRecord.startDate) {
      date2 = DateHelper.copyTimeValues(me.clearTime(date2), eventRecord.startDate);
    }
    date2 = me.applyDragOffset(date2, drag);
    if (drag.peek("sourceStore") !== me.view.eventStore && !eventRecord.startDate) {
      const autoCreate = drag.target.view.autoCreate || me.owner.client.autoCreate;
      date2.setHours((autoCreate == null ? void 0 : autoCreate.startHour) || 8);
    }
    await me.setStartDate(eventRecord, date2);
  }
  pickDropTarget(drag) {
    let hit = this.hitTest(drag);
    if (!hit && this.isResizing) {
      hit = this.hit;
    }
    return (hit == null ? void 0 : hit.date) ? hit : null;
  }
  setStartDate(eventRecord, date2) {
    return eventRecord.set({
      startDate: date2,
      endDate: DateHelper.add(date2, eventRecord.fullDuration)
    });
  }
  // Hoverable
  getHoverHandleCls(other) {
    return other ? "" : "b-gripper-vert";
  }
  hoverEnter() {
    var _a5, _b2;
    const me = this, hit = me.hitTest(me.hoverTarget), { gripper } = me.owner, cls = me.getHoverHandleCls(), otherCls = me.getHoverHandleCls(true);
    otherCls && gripper.classList.remove(otherCls);
    if (((_a5 = hit == null ? void 0 : hit.eventRecord) == null ? void 0 : _a5.resizable) !== false && !((_b2 = hit == null ? void 0 : hit.eventRecord) == null ? void 0 : _b2.readOnly)) {
      cls && gripper.classList.add(cls);
      me.hoverTarget.appendChild(gripper);
    }
  }
  hoverLeave(leaving) {
    const me = this, { gripper } = me.owner, cls = me.getHoverHandleCls();
    if (gripper.parentNode === leaving) {
      cls && gripper.classList.remove(cls);
      leaving.removeChild(gripper);
    }
  }
  // Misc
  applyDragOffset(date2, drag) {
    var _a5;
    const eventOffset = drag.peek("eventOffset");
    if (date2 && (eventOffset == null ? void 0 : eventOffset[0])) {
      if (drag.source.constructor === ((_a5 = drag.target) == null ? void 0 : _a5.constructor)) {
        date2 = DateHelper.add(date2, -eventOffset[0], eventOffset[1]);
      }
    }
    return date2;
  }
  hitTest(at) {
    const me = this, isDragContext = at == null ? void 0 : at.isDragContext, event = isDragContext ? at.event : at, target = isDragContext && at.targetElement || DomHelper.getEventElement(event);
    let src = me.view, hit = null, edge, wrapEl;
    if (target) {
      if (!src.calendarHitTest) {
        src = me.owner.client;
      }
      const horizontalStartEdge = src.rtl ? "right" : "left";
      hit = src.calendarHitTest(target);
      if (hit) {
        hit.eventWrap = wrapEl = target.closest(".b-cal-event-wrap");
        if (target.classList.contains("b-gripper")) {
          hit = {
            type: "resize",
            cell: hit.cell,
            date: hit.date,
            edge: edge = {},
            eventRecord: hit.eventRecord,
            resource: hit.resource,
            eventWrap: wrapEl,
            gripper: target,
            view: hit.view
          };
          DomClassList.normalize(wrapEl.className, "array").forEach((c) => {
            c = edgeRe.exec(c);
            c && (edge[c[1]] = true);
          });
          hit.atEnd = !(edge.top || edge[horizontalStartEdge]);
        } else if (!hit.eventRecord) {
          wrapEl = null;
        }
        if (wrapEl) {
          hit.eventTop = wrapEl.style.top;
        }
        if (target !== event) {
          hit.date = me.getDateFromPosition(event.clientX, event.clientY) || hit.date;
        }
      }
    }
    return hit;
  }
  renderEvent(eventRecord, first, last) {
    var _a5, _b2;
    const { view, owner } = this;
    Object.defineProperty(eventRecord, "assigned", this.assignmentsProperty);
    const dom = view.createEventDomConfig({
      eventRecord,
      isAllDay: isAllDayView[view.type],
      asSelected: eventRecord.data.realEventId && ((_b2 = (_a5 = owner.client).isEventSelected) == null ? void 0 : _b2.call(_a5, eventRecord.data.realEventId))
    });
    dom.className["b-cal-tentative-event"] = 1;
    dom.className["b-cal-tentative-event-first"] = first;
    dom.className["b-cal-tentative-event-last"] = last;
    dom.retainElement = true;
    delete eventRecord.assigned;
    if (last) {
      const { footer } = owner;
      if (footer && !view.showTime.endTime) {
        appendEventFooter(dom, ObjectHelper.assign({
          html: DateHelper.format(eventRecord.endDate, view.timeFormat)
        }, footer));
      }
    }
    return dom;
  }
  setEventData(data, creating) {
    const me = this, { eventRecord, view } = me, { duration, startDate } = data;
    if (startDate && duration != null && !data.endDate) {
      data = {
        ...data,
        endDate: DateHelper.add(startDate, duration, eventRecord.durationUnit)
      };
    }
    eventRecord.set(data);
    if (me.eventDom) {
      me.eventDom.className[view.shortEventCls] = eventRecord.rawDurationMS <= view.shortEventDuration;
    } else {
      if (creating) {
        let name, { newName } = me.owner;
        if (newName) {
          const newNameFn = me.view.resolveCallback(newName, view, false);
          name = (newNameFn == null ? void 0 : newNameFn.handler.call(view, eventRecord)) || newName;
        } else {
          newName = view.autoCreate.newName;
          name = view.resolveCallback(newName, view, false) ? view.callback(newName, view, [view, eventRecord.startDate, view.resource || view.defaultCalendar]) : newName;
        }
        eventRecord.name = name;
      }
      me.eventDom = me.renderEvent(eventRecord);
    }
  }
  setupEvent(data, creating) {
    var _a5;
    const me = this, { owner } = me, {
      defaultCalendar,
      eventStore
    } = me.view, { tooltip } = owner, eventRecord = me.eventRecord = owner.eventRecord = eventStore.createRecord({
      // must pass empty object for CalendarStores hook of createRecord() to set resourceId
      // and because the data passed is field *names*, not dataSources
    }), assignments = creating ? /* @__PURE__ */ new Set([eventStore.assignmentStore.createRecord({
      event: eventRecord,
      resource: defaultCalendar
    })]) : data.assigned;
    me.assignmentsProperty = {
      configurable: true,
      value: assignments
    };
    delete data.assigned;
    if (data[metaProp]) {
      eventRecord.meta = data[metaProp];
    }
    me.setEventData(data, creating);
    if (me.isCreating && defaultCalendar) {
      eventStore.assignmentStore.assignEventToResource(eventRecord, defaultCalendar);
    }
    const { eventDom } = me;
    eventDom.className[tentativeCls] = 1;
    if (!me.noTip && !tooltip.disabled && ((_a5 = me.dragging) == null ? void 0 : _a5.has("eventCreate"))) {
      tooltip.eventRecord = eventRecord;
      tooltip.recurrenceHint = creating && me.recurrable ? owner.recurrenceTip : "";
      me.dragProxy.tooltip = tooltip;
    }
    return eventDom;
  }
  startCreate(date2) {
    this.createEvent({
      allDay: true,
      startDate: date2,
      durationUnit: this.durationUnit,
      endDate: DateHelper.add(date2, 1, "d")
    });
  }
  get durationUnit() {
    return this.view.dragUnit || Duration.from(this.view.autoCreate.duration).unit || this.owner.durationUnit;
  }
  startMove(eventRecord) {
    var _a5, _b2, _c2, _d;
    const me = this, data = ObjectHelper.clone(eventRecord.dataByFieldName), drag = me.dropping, { hit } = me, color = (_c2 = (_a5 = hit == null ? void 0 : hit.resource) == null ? void 0 : _a5.eventColor) != null ? _c2 : (_b2 = drag.peek("resource")) == null ? void 0 : _b2.eventColor;
    if (color && !data.eventColor) {
      data.eventColor = color;
    }
    data.assigned = eventRecord.assigned;
    if (!data.startDate) {
      data.startDate = (hit == null ? void 0 : hit.date) || /* @__PURE__ */ new Date();
      data.endDate = DateHelper.add(data.startDate, eventRecord.duration || 1, eventRecord.durationUnit || "h");
    }
    if (hit == null ? void 0 : hit.resource) {
      data.resourceId = hit.resource.id;
    }
    if (!data.resourceId) {
      data.resourceId = drag.peek("resourceId") || ((_d = me.owner.client) == null ? void 0 : _d.defaultCalendarId);
    }
    data.realEventId = data.id;
    delete data.id;
    data[metaProp] = ObjectHelper.clone(eventRecord.meta);
    me.setupEvent(data);
  }
  startResize(eventHit) {
    var _a5;
    const { eventRecord } = eventHit, data = ObjectHelper.clone(eventRecord.dataByFieldName);
    data[metaProp] = ObjectHelper.clone(eventRecord.meta);
    this.createEvent({
      ...data,
      // Include the Set of assignments in the data for the tentative event
      assigned: eventRecord.assigned,
      id: `dragResize-event-${data.id}`,
      eventColor: data.eventColor || ((_a5 = eventRecord.resource) == null ? void 0 : _a5.eventColor),
      recurrenceRule: null,
      realEventId: data.id
    });
  }
  // Configs
  configureListeners(drag) {
    const listeners = super.configureListeners(drag);
    listeners.element = this.view.rootElement;
    return listeners;
  }
  updateHit(hit) {
    const me = this, drag = me.dropping, mode = hit && drag[eventDragSym];
    if (hit) {
      if (me.isMoving) {
        me.dropHitMove(drag, hit, drag.peek("eventRecord"));
      } else if (mode == null ? void 0 : mode.create) {
        me.dropHitCreate(drag, hit, drag.peek("eventCreate"));
      } else if (mode == null ? void 0 : mode.resize) {
        me.dropHitResize(drag, hit, drag.peek("eventSourceHit"));
      }
    } else {
      me.dropHitNowhere(drag);
    }
  }
  updateDays(days2) {
    const me = this, { calendarCells, eventDom, dayValues } = me, newDayValues = calendarCells && {};
    let first = true, cell, date2, dayValue, i, lastCell;
    me.dayValues = newDayValues;
    if (calendarCells && eventDom) {
      for (i = 0; i < calendarCells.length; ++i) {
        cell = calendarCells[i];
        if (days2 == null ? void 0 : days2.includes(cell.dataset.date)) {
          lastCell = cell;
        }
      }
      for (i = 0; i < calendarCells.length; ++i) {
        cell = calendarCells[i];
        date2 = cell.dataset.date;
        if (days2 == null ? void 0 : days2.includes(date2)) {
          if (!(dayValue = dayValues == null ? void 0 : dayValues[date2])) {
            dayValue = me.includeDay(date2, cell, first, cell === lastCell) || true;
            first = false;
          } else {
            delete dayValues[date2];
          }
          newDayValues[date2] = dayValue;
        }
      }
      if (dayValues) {
        const cleanDom = () => {
          for (i in dayValues) {
            me.removeDay(i, dayValues[i], me.isCreating);
          }
        };
        if (!days2 && me.isCreating && !me.dragging.aborted) {
          me.view.afterRefresh(cleanDom);
        } else {
          cleanDom();
        }
      }
    }
  }
  updateEventRecord(record) {
    this.owner.eventRecord = record;
  }
  updateOverflow(value2, instance) {
    if (!value2 && instance) {
      instance.destroy();
    }
    return value2;
  }
  updateOwner(owner) {
    this.hoverIgnoreElement = owner == null ? void 0 : owner.gripper;
  }
  updateRootElement(rootEl) {
    const me = this;
    me.dragRootElement = rootEl;
    me.dropRootElement = me.droppable ? rootEl : null;
    if (!me.owner.resizable) {
      me.hoverable = false;
    }
    me.hoverRootElement = me.hoverable ? rootEl : null;
  }
  updateView(view, was) {
    var _a5;
    super.updateView(view, was);
    const me = this;
    me.rootElement = view ? me.findRootElement(view) : null;
    (_a5 = me._overflowDetacher) == null ? void 0 : _a5.call(me);
    if (view == null ? void 0 : view.isDayCellRenderer) {
      me._overflowDetacher = view.ion({
        thisObj: me,
        showOverflowPopup: "onShowOverflowPopup"
      });
    }
  }
};
__publicField(Zone, "$name", "Zone");
Zone.prototype._eventRecord = null;
var BaseDayZone = class extends Zone {
  static get configurable() {
    return {
      hoverSelector: ".b-cal-event-wrap",
      draggingClsSelector: ".b-dayview-content"
    };
  }
  getHoverHandleCls(other) {
    let vert = this.isAllDayZone;
    if (other) {
      vert = !vert;
    }
    return `b-gripper-${vert ? "vert" : "horz"}`;
  }
};
__publicField(BaseDayZone, "$name", "BaseDayZone");
var AllDayZone = class extends BaseDayZone {
  static get configurable() {
    return {
      hoverEdges: "lr"
    };
  }
  // Drag handling
  dragEnter(drag) {
    if (!this.view.eventsPerCell) {
      this.view.expandGutter();
    }
    return super.dragEnter(drag);
  }
  dragLeave(drag) {
    this.view.collapseGutter();
    super.dragLeave(drag);
  }
  captureDragOffset(eventRecord, hit, drag) {
    drag.set("eventOffset", [
      Math.max(Math.floor(DateHelper.diff(eventRecord.startDate, hit.date, "d")), 0),
      "d"
    ]);
  }
  // Drop handling
  dropHitCreate(drag, hit, dragFrom) {
    let endDate = this.clearTime(hit.date), startDate = this.clearTime(dragFrom.date);
    if (endDate < startDate) {
      [startDate, endDate] = [endDate, startDate];
    }
    this.setEventData({
      startDate,
      duration: DateHelper.as(this.eventRecord.durationUnit, DateHelper.diff(startDate, endDate, "d") + 1, "d")
    });
  }
  async moveEventTo(drag, dropRec) {
    const me = this, hit = me.hitTest(drag), date2 = me.applyDragOffset(hit == null ? void 0 : hit.date, drag), newDate = new Date(dropRec.startDate);
    if (date2) {
      newDate.setFullYear(date2.getFullYear());
      newDate.setMonth(date2.getMonth());
      newDate.setDate(date2.getDate());
      if (me.view.dayTime.startShift) {
        newDate.setHours(date2.getHours());
        newDate.setMinutes(date2.getMinutes());
        newDate.setSeconds(date2.getSeconds());
        dropRec.duration = 1;
      } else {
        if (!me.view.isAllDayEvent(dropRec)) {
          dropRec.allDay = true;
        }
      }
      await me.setStartDate(dropRec, newDate);
    }
  }
  // Misc
  setEventData(data, creating) {
    super.setEventData(data, creating);
    const me = this, { eventRecord } = me;
    const { startDate, endDate } = eventRecord;
    if (creating && !me.view.dayTime.startShift) {
      eventRecord.allDay = true;
    }
    me.days = me.makeDays(startDate, endDate);
  }
  // Configs
  updateDays(days2) {
    var _a5, _b2;
    const me = this;
    let { eventEl } = me;
    if (days2 == null ? void 0 : days2.length) {
      if (!eventEl) {
        me.eventEl = eventEl = DomHelper.createElement({
          ...me.eventDom
        });
        eventEl.classList.add("b-allday");
      }
      const { dropRootElement } = me, { visibleCellSelector, weekLength } = me.view, eventTop = (_b2 = (_a5 = me.dragging) == null ? void 0 : _a5.peek("eventSourceHit")) == null ? void 0 : _b2.eventTop, cells = DomHelper.children(dropRootElement, visibleCellSelector), cell = DomHelper.down(dropRootElement, `${visibleCellSelector}[data-date='${days2[days2.length - 1]}']`);
      DomHelper.down(cell, ".b-cal-event-bar-container").appendChild(eventEl);
      eventEl.style[me.view.rtl ? "right" : "left"] = DomHelper.percentify(100 * (cells.indexOf(cell) - days2.length + 1) / weekLength);
      eventEl.style.width = DomHelper.percentify(100 * days2.length / weekLength);
      if (eventTop) {
        eventEl.style.top = eventTop;
      }
      me.view.scrollable.scrollIntoView(eventEl, true);
    } else if (eventEl) {
      if (eventEl.classList.contains("b-cal-tentative-event")) {
        eventEl.remove();
      }
      me.eventEl = null;
    }
  }
  updateView(view, was) {
    if (view) {
      const multiDay = DateHelper.diff(view.startDate, view.endDate, "d") > 1;
      this.hoverable = multiDay;
      this.draggable = multiDay || this.view.owner.isDayView;
    }
    super.updateView(view, was);
  }
};
__publicField(AllDayZone, "$name", "AllDayZone");
var DayZone = class extends BaseDayZone {
  construct(...args) {
    super.construct(...args);
    const me = this, { allDayEvents } = me.view;
    if (allDayEvents && me.view.showAllDayHeader !== false) {
      me.allDayZone = new me.allDayZoneClass({
        owner: me.owner,
        view: allDayEvents,
        resource: me.resource
      });
    }
  }
  syncDraggingElements(eventId, active) {
    var _a5;
    const { dragging: drag } = this, { draggingItemCls } = drag.source, containerEl = drag.itemElement.closest(".b-dayview-day-container"), elements = (_a5 = containerEl == null ? void 0 : containerEl.querySelectorAll(`[data-event-id="${eventId}"]`)) != null ? _a5 : [];
    for (const el of elements) {
      el.classList.toggle(draggingItemCls, active);
    }
  }
  hitTest(at) {
    const {
      view,
      dragging
    } = this, { dayTime } = view, hit = super.hitTest(at);
    if (hit) {
      const { date: date2 } = hit, overDate = (at == null ? void 0 : at.isDragContext) ? view.getDateFromPosition(at.event.clientX, Rectangle.fromScreen(view.dayContainerElement).y + 1, false, dayTime) : dayTime.startOfDay(hit.date);
      if (dragging && (!this.isMoving && view.interDayResize === false) || this.isMoving && view.interDayDrag === false) {
        const startDate = dragging.peek("date");
        date2.setDate(startDate.getDate());
        date2.setMonth(startDate.getMonth());
        date2.setFullYear(startDate.getFullYear());
        if (overDate) {
          overDate.setDate(startDate.getDate());
          overDate.setMonth(startDate.getMonth());
          overDate.setFullYear(startDate.getFullYear());
        }
      }
      hit.overDate = overDate;
    }
    return hit;
  }
  updateDragEventId(eventId, previousEventId) {
    previousEventId && this.syncDraggingElements(previousEventId, false);
    eventId && this.syncDraggingElements(eventId, true);
  }
  get eventRecord() {
    var _a5;
    return super.eventRecord || ((_a5 = this.allDayZone) == null ? void 0 : _a5.eventRecord);
  }
  set eventRecord(value2) {
    super.eventRecord = value2;
  }
  get recurring() {
    var _a5;
    return this.isCreating && ((_a5 = this.eventRecord) == null ? void 0 : _a5.recurrenceRule) != null;
  }
  get droppingAllDay() {
    var _a5, _b2;
    const eventRecord = (_a5 = this.dropping) == null ? void 0 : _a5.peek("eventRecord");
    return (eventRecord == null ? void 0 : eventRecord.startDate) && ((_b2 = this.view) == null ? void 0 : _b2.isAllDayEvent(eventRecord)) || false;
  }
  get useAllDay() {
    return this.droppingAllDay && this.view.showAllDayHeader && this.dropping.source !== this.allDayZone;
  }
  get wasAllDay() {
    return this.droppingAllDay && this.dropping.source === this.allDayZone;
  }
  doDestroy() {
    var _a5;
    (_a5 = this.allDayZone) == null ? void 0 : _a5.destroy();
    super.doDestroy();
  }
  dragStart() {
    var _a5, _b2;
    super.dragStart();
    this.dragEventId = (_b2 = (_a5 = this.dragging.peek("eventRecord")) == null ? void 0 : _a5.id) != null ? _b2 : null;
  }
  findRootElement(view) {
    return view.eventContentElement;
  }
  // Drag handling
  captureDragOffset(eventRecord, hit, drag) {
    drag.set("eventOffset", [
      Math.floor(DateHelper.diff(eventRecord.startDate, hit.date, "m")),
      "m"
    ]);
  }
  cleanupDrag() {
    var _a5;
    this.dragEventId = null;
    super.cleanupDrag();
    (_a5 = this.allDayZone) == null ? void 0 : _a5.cleanupDrag();
  }
  // Drop handling
  cleanupDrop() {
    var _a5, _b2, _c2;
    super.cleanupDrop();
    (_b2 = (_a5 = this.owner.client) == null ? void 0 : _a5._scrollManager) == null ? void 0 : _b2.stopMonitoring();
    (_c2 = this.allDayZone) == null ? void 0 : _c2.cleanupDrop();
  }
  dropHitCreate(drag, hit) {
    const me = this, { durationUnit } = me.eventRecord;
    let endTime = hit.date, startTime = drag.peek("eventCreate").date, endDate = endTime, startDate = startTime, duration, recurrenceCount;
    const sameDay = !(me.clearTime(startTime) - me.clearTime(endTime)), recurring = drag.ctrlKey && !sameDay;
    if (recurring || sameDay) {
      endDate = me.clearTime(endTime);
      startDate = me.clearTime(startTime);
      startTime = startTime - startDate;
      endTime = endTime - endDate;
      if (endDate < startDate) {
        [startDate, endDate] = [endDate, startDate];
      }
      if (endTime < startTime) {
        [startTime, endTime] = [endTime, startTime];
      }
      startDate.setTime(startDate.getTime() + startTime);
      duration = DateHelper.as(durationUnit, Math.max(me.view.increment, Math.floor(endTime - startTime)));
      if (recurring) {
        recurrenceCount = DateHelper.diff(me.clearTime(startDate), me.clearTime(endDate), "d") + 1;
      }
    } else {
      if (endDate < startDate) {
        [startDate, endDate] = [endDate, startDate];
      }
      duration = DateHelper.as(durationUnit, Math.floor((endDate - startDate) / MINUTES), "m");
    }
    me.setEventData({
      startDate,
      duration,
      recurrenceRule: recurring ? `FREQ=DAILY;COUNT=${recurrenceCount}` : null
    });
  }
  dropHitMove(drag, hit, eventRecord) {
    let startDate = hit.date;
    const me = this, { view } = me, dayStart = view.dayTime.startOfDay(startDate);
    if (me.useAllDay) {
      me.allDayZone.dropHitMove(drag, hit, eventRecord);
    } else {
      eventRecord = me.eventRecord;
      startDate = me.applyDragOffset(startDate, drag);
      if (view.showAllDayHeader && startDate < dayStart) {
        startDate = dayStart;
      } else if (!view.snapRelativeToEventStartDate) {
        const snapType = drag.event.clientX > drag.startEvent.clientX ? "ceil" : "floor";
        startDate = DateHelper[snapType](startDate, view.increment);
      }
      me.setEventData({
        startDate,
        endDate: DateHelper.add(startDate, eventRecord.duration, eventRecord.durationUnit),
        duration: eventRecord.duration
      });
    }
  }
  applyDragOffset(date2, drag) {
    let result = super.applyDragOffset(date2, drag);
    if (this.constrainToDay) {
      const { overDate } = this.hit;
      result = new Date(
        Math.max(
          Math.min(result, overDate.getTime() + this.view.dayTime.timeEnd - this.eventRecord.rawDurationMS),
          overDate
        )
      );
    }
    return result;
  }
  dropHitResize(drag, hit, eventHit) {
    const me = this, { eventRecord } = me, date2 = hit.date;
    if (eventHit.atEnd) {
      if (eventRecord.startDate < date2) {
        me.setEventData({
          endDate: date2
        });
      }
    } else if (date2 < eventRecord.endDate) {
      me.setEventData({
        startDate: date2
      });
    }
  }
  async moveEventTo(drag, dropRec) {
    const me = this, hit = me.hitTest(drag), date2 = hit == null ? void 0 : hit.date;
    if (date2) {
      if (me.useAllDay) {
        await me.allDayZone.moveEventTo(drag, dropRec);
      } else {
        const { view } = me, newDate = me.applyDragOffset(date2, drag), snapType = drag.event.clientX > drag.startEvent.clientX ? "ceil" : "floor", newStartDate = view.snapRelativeToEventStartDate ? newDate : DateHelper[snapType](newDate, view.increment);
        if (me.wasAllDay) {
          const endOfDay = DateHelper.add(view.dayTime.startOfDay(newStartDate), 24, "h"), newEndDate = DateHelper.add(newStartDate, dropRec.duration, dropRec.durationUnit);
          dropRec.allDay = false;
          if (newEndDate > endOfDay) {
            const autoCreate = view.autoCreate || view.changeAutoCreate(true), defaultDuration = DateHelper.parseDuration(autoCreate.duration), newEndDate2 = new Date(Math.min(DateHelper.add(newStartDate, defaultDuration.magnitude, defaultDuration.unit), endOfDay)), newDuration = DateHelper.diff(newStartDate, newEndDate2, dropRec.durationUnit);
            dropRec.setDuration(newDuration, dropRec.unit);
          }
        }
        await me.setStartDate(dropRec, newStartDate);
      }
    }
  }
  startCreate(date2) {
    this.createEvent({
      duration: 0,
      durationUnit: this.durationUnit,
      startDate: date2,
      // It's only a provisional event until gesture is completed (possibly longer if an editor dialog is shown after)
      isCreating: true
    });
  }
  // Misc
  includeDay(date2, cell, first, last) {
    return DomHelper.createElement({
      parent: cell.querySelector(".b-dayview-event-container"),
      ...this.renderEvent(this.eventRecord, first, last)
    });
  }
  removeDay(date2, value2) {
    if (value2.classList.contains("b-cal-tentative-event")) {
      value2.remove();
    }
  }
  setEventData(data, creating) {
    const me = this;
    if (me.useAllDay) {
      me.allDayZone.setEventData(data, creating);
      return;
    }
    super.setEventData(data, creating);
    const { eventRecord } = me, { endDate, startDate } = eventRecord, { dayTime } = me.view;
    let lastDate = endDate;
    if (me.recurring) {
      lastDate = DateHelper.add(lastDate, eventRecord.recurrence.count - 1, "d");
    }
    me.days = me.makeDays(startDate, lastDate);
    me.times = [
      dayTime.delta(startDate, "s"),
      dayTime.delta(endDate, "s")
    ];
  }
  setupEvent(data, creating) {
    const me = this;
    if (me.useAllDay) {
      me.allDayZone.setupEvent(data, creating);
    } else {
      if (this.wasAllDay) {
        data.allDay = false;
        data.endDate = DateHelper.add(data.startDate, data.duration = 1, data.durationUnit = "hour");
      }
      super.setupEvent(data, creating);
    }
  }
  // Configs
  updateDays(days2, was) {
    super.updateDays(days2, was);
    this.updateTimes(this.times, null);
  }
  updateTimes(times) {
    if (!times) {
      return;
    }
    const me = this, { allDayZone, dayValues, dragging, eventRecord, isResizing, recurring, view } = me, { dayTime, eventSpacing } = view, { endDate, startDate } = eventRecord, [startOffset, endOffset] = times, eventInset = !(dragging == null ? void 0 : dragging.aborted) && (dragging == null ? void 0 : dragging.peek("eventInset")), eventWidth = !(dragging == null ? void 0 : dragging.aborted) && (dragging == null ? void 0 : dragging.peek("eventWidth")), insetProp = view.rtl ? "right" : "left", firstDay = dayTime.dateKey(startDate), lastDay = dayTime.dateKey(endDate), multiDay = dayTime.startOfDay(startDate) < dayTime.startOfDay(endDate), heightScale = 100 / dayTime.duration("s");
    let { days: days2 } = me, date2, dayEl, first, height, rect1, rect2, style, top;
    if (!days2) {
      return;
    }
    for (date2 in dayValues) {
      dayEl = dayValues[date2];
      first = date2 === firstDay;
      style = dayEl.style;
      top = startOffset * heightScale;
      height = (endOffset - startOffset) * heightScale;
      DomSync.sync({
        targetElement: dayEl,
        domConfig: me.renderEvent(me.eventRecord, first, date2 === days2[days2.length - 1])
      });
      if (!recurring && multiDay) {
        if (first) {
          height = 100 - top;
        } else if (date2 === lastDay) {
          height = top + height;
        } else {
          height = 100;
        }
      }
      style.top = recurring || first ? DomHelper.percentify(top) : 0;
      style.height = DomHelper.percentify(height);
      style.paddingBottom = DomHelper.setLength(eventSpacing);
      if (isResizing) {
        if (eventInset) {
          style[insetProp] = eventInset;
        }
        if (eventWidth) {
          style.width = `${eventWidth}px`;
        }
      }
      rect1 = Rectangle.from(dayEl.querySelector(".b-cal-event-desc"));
      if (rect1) {
        rect2 = Rectangle.from(dayEl.querySelector(".b-cal-event-footer"));
        rect2 && dayEl.classList.toggle("b-cal-event-footer-desc-overlap", Boolean(rect1.intersect(rect2)));
      }
    }
    if (!days2 || days2.length < 2 || recurring) {
      allDayZone == null ? void 0 : allDayZone.cleanupDrop();
      days2 = null;
    } else if (allDayZone && view.showAllDayHeader) {
      if (!allDayZone.eventRecord) {
        allDayZone.noTip = true;
        const eventData = {
          startDate: eventRecord.startDate,
          endDate: eventRecord.endDate
        };
        if (isResizing) {
          const resizeEvent = dragging.peek("eventRecord");
          eventData.assigned = resizeEvent.assigned;
          eventData.eventColor = resizeEvent.eventColor;
        }
        allDayZone.createEvent(eventData, me.dropping);
      }
      allDayZone.eventRecord.set({
        startDate: eventRecord.startDate,
        endDate: DateHelper.add(eventRecord.startDate, days2.length - 1, "d")
      });
      allDayZone.days = days2;
    }
  }
};
__publicField(DayZone, "$name", "DayZone");
__publicField(DayZone, "configurable", {
  dragEventId: null,
  hoverable: true,
  hoverEdges: "tb",
  times: {
    $config: {
      equal: "array"
    },
    value: null
  },
  allDayZoneClass: AllDayZone,
  // When dragging events, configure the DayZone with constrainToDay : true
  // to prevent dragging events so that they cross the day start or end boundaries.
  constrainToDay: false
});
var DayAgendaZone = class extends DayZone {
  static get $name() {
    return "DayAgendaZone";
  }
  findRootElement() {
    return this.view.eventContentElement;
  }
  // captureDragOffset(eventRecord, hit, drag) {
  //     drag.set('eventOffset', [
  //         DH.diff(eventRecord.startDate, hit.date, 'm'),
  //         'm'
  //     ]);
  // }
  hitTest(at) {
    var _a5, _b2;
    const result = Zone.prototype.hitTest.call(this, at);
    if (result) {
      result.hourElement = (_b2 = ((_a5 = at.event || at.domEvent) == null ? void 0 : _a5.target) || at) == null ? void 0 : _b2.closest("[data-hour]");
    }
    return result;
  }
  setEventData() {
    return Zone.prototype.setEventData.apply(this, arguments);
  }
  dropHitMove(drag, hit, eventRecord) {
    let startDate = hit.date;
    const me = this, { view } = me, dayStart = view.dayTime.startOfDay(startDate);
    eventRecord = me.eventRecord;
    if (startDate < dayStart) {
      startDate = dayStart;
    } else if (!view.snapRelativeToEventStartDate) {
      const snapType = drag.event.clientX > drag.startEvent.clientX ? "ceil" : "floor";
      startDate = DateHelper[snapType](startDate, view.increment);
    }
    me.setEventData({
      startDate,
      endDate: DateHelper.add(startDate, eventRecord.duration, eventRecord.durationUnit),
      duration: eventRecord.duration
    });
  }
  async moveEventTo(drag, dropRec) {
    const me = this, {
      date: date2,
      hourElement
    } = me.hitTest(drag);
    if (date2 && hourElement) {
      const { view } = me, snapType = drag.event.clientX > drag.startEvent.clientX ? "ceil" : "floor", newStartDate = view.snapRelativeToEventStartDate ? date2 : DateHelper[snapType](date2, view.increment);
      if (me.wasAllDay) {
        const endOfDay = DateHelper.add(view.dayTime.startOfDay(newStartDate), 24, "h"), newEndDate = DateHelper.add(newStartDate, dropRec.duration, dropRec.durationUnit);
        dropRec.allDay = false;
        if (newEndDate > endOfDay) {
          const autoCreate = view.autoCreate || view.changeAutoCreate(true), defaultDuration = DateHelper.parseDuration(autoCreate.duration), newEndDate2 = new Date(Math.min(DateHelper.add(newStartDate, defaultDuration.magnitude, defaultDuration.unit), endOfDay)), newDuration = DateHelper.diff(newStartDate, newEndDate2, dropRec.durationUnit);
          dropRec.setDuration(newDuration, dropRec.unit);
        }
      }
      await me.setStartDate(dropRec, newStartDate);
    }
  }
};
__publicField(DayAgendaZone, "configurable", {
  hoverable: false,
  dragProxy: {
    type: "default",
    open(drag) {
      const me = this, { source } = drag, sourceEl = drag.element.closest(".b-cal-event-wrap");
      me.proxyEl = sourceEl.cloneNode(true);
      me.proxyEl.classList.add("b-cal-drag-proxy");
      me.proxyEl.style.width = `${sourceEl.offsetWidth}px`;
      me.proxyEl.style.setProperty("--cal-event-color", sourceEl.style.getPropertyValue("--cal-event-color"));
      me.proxyOffset = EventHelper.getClientPoint(drag.startEvent).getDelta(Rectangle.from(sourceEl));
      source.view.firstVisibleCell.appendChild(me.proxyEl);
    },
    dragMove(drag) {
      const { source } = drag, { view, eventRecord } = source;
      if (source.hit && this.proxyEl) {
        DomSync.sync({
          targetElement: this.proxyEl,
          domConfig: {
            ...source.renderEvent(eventRecord),
            onlyChildren: true
          }
        });
        DomHelper.alignTo(this.proxyEl, EventHelper.getClientPoint(drag.event).translate(20, 20), {
          align: "t0-t0",
          constrainTo: source.dragRootElement
        });
        view.overHour = source.hit.date;
      } else {
        view.overHour = null;
      }
    },
    close(drag) {
      var _a5;
      drag.source.view.overHour = null;
      (_a5 = this.proxyEl) == null ? void 0 : _a5.remove();
    }
  }
});
var ResourceCalendarRowZone = class extends AllDayZone {
  // Disable Hoverable behaviour. Resizing is not allowed.
  // Event bars are discontiguous, so resizing an event across multiple
  // cells has ambiguous semantics. We just disallow it.
  // Events in this view may only be *moved*
  syncHoverListeners() {
  }
  beforeDrag(drag) {
    const hit = this.hitTest(drag);
    if (!(hit == null ? void 0 : hit.resource) || hit.type === "resize" || hit.type === "schedule") {
      return false;
    }
    return super.beforeDrag(drag);
  }
  setupEvent() {
    this.view.defaultCalendar = (this.dragging || this.dropping).peek("resource");
    return super.setupEvent(...arguments);
  }
  updateHit(hit, was) {
    if (!hit || hit.resource) {
      super.updateHit(hit, was);
    }
  }
  makeDays(startDate) {
    var _a5;
    const me = this, { isMoving } = me, drag = me.dragging || me.dropping, date2 = isMoving ? new Date(Math.max(startDate, me.view.firstVisibleDate)) : drag.peek("date"), result = super.makeDays(date2, DateHelper.add(date2, 1, "d"));
    result.resource = (_a5 = me.hit) == null ? void 0 : _a5.resource;
    return result;
  }
  updateDays(days2) {
    var _a5, _b2;
    const me = this;
    let { view, eventEl } = me;
    if (days2 == null ? void 0 : days2.length) {
      if (!eventEl) {
        me.eventEl = eventEl = DomHelper.createElement({
          ...me.eventDom
        });
        eventEl.classList.add("b-allday");
      }
      const eventTop = (_b2 = (_a5 = me.dragging) == null ? void 0 : _a5.peek("eventSourceHit")) == null ? void 0 : _b2.eventTop, resourceCell = DomHelper.down(
        me.dropRootElement,
        `${view.visibleCellSelector}:is(${days2.map((d) => `[data-date="${d}"]`).join(",")}) [data-resource-id="${days2.resource.id}"] > .b-cal-event-bar-container`
      );
      resourceCell.appendChild(eventEl);
      eventEl.style.width = "100%";
      if (eventTop) {
        eventEl.style.top = eventTop;
      }
      view.scrollable.scrollIntoView(eventEl, true);
    } else if (eventEl) {
      if (eventEl.classList.contains("b-cal-tentative-event")) {
        eventEl.remove();
      }
      me.eventEl = null;
    }
  }
};
__publicField(ResourceCalendarRowZone, "$name", "ResourceCalendarRowZone");
__publicField(ResourceCalendarRowZone, "configurable", {
  droppableSelector: ".b-calendarrow-cell-container,.b-dayresourcecalendarrow-cell-resources,.b-dayresource-allday.b-cal-cell-header"
});
var DayResourceZone = class extends DayZone {
  beforeDrag(drag) {
    const hit = this.hitTest(drag);
    if (!(hit == null ? void 0 : hit.resource)) {
      return false;
    }
    return super.beforeDrag(drag);
  }
  setupEvent() {
    this.view.defaultCalendar = (this.dragging || this.dropping).peek("resource");
    return super.setupEvent(...arguments);
  }
  hitTest(at) {
    const drag = this.dragging || this.dropping, result = super.hitTest(at);
    if (result && drag && !this.isMoving) {
      const fromDate = drag.peek("date");
      result.resource = drag.peek("resource");
      result.date.setDate(fromDate.getDate());
      result.date.setMonth(fromDate.getMonth());
      result.date.setFullYear(fromDate.getFullYear());
    }
    return result;
  }
  updateHit(hit, was) {
    if (!hit || hit.resource) {
      super.updateHit(hit, was);
    }
  }
  makeDays(startDate, endDate) {
    var _a5;
    const me = this, { isMoving } = me, drag = me.dragging || me.dropping, date2 = isMoving ? startDate : drag.peek("date"), result = super.makeDays(date2, DateHelper.add(date2, 1, "d"));
    result.resource = (_a5 = me.hit) == null ? void 0 : _a5.resource;
    return result;
  }
  updateDays(days2) {
    const me = this;
    let { eventEl } = me;
    if (days2 == null ? void 0 : days2.length) {
      const cell = DomHelper.down(
        me.dropRootElement,
        `:is(${days2.map((d) => `[data-date="${d}"]`).join(",")}) ${me.view.visibleCellSelector}[data-resource-id="${days2.resource.id}"] .b-dayview-event-container`
      );
      if (!eventEl) {
        me.eventEl = eventEl = DomHelper.createElement({
          ...me.eventDom
        });
      }
      cell.appendChild(eventEl);
    } else if (eventEl) {
      if (eventEl.classList.contains("b-cal-tentative-event")) {
        me.view.setTimeout(() => eventEl.remove(), 100);
      }
      me.eventEl = null;
    }
    me._times = null;
  }
  updateTimes(times) {
    if (!times || !this.days) {
      return;
    }
    const me = this, { dayTime, eventSpacing } = me.view, [startOffset, endOffset] = times, heightScale = 100 / dayTime.duration("s"), { style } = me.eventEl, top = startOffset * heightScale, height = (endOffset - startOffset) * heightScale;
    DomSync.sync({
      targetElement: me.eventEl,
      domConfig: me.renderEvent(me.eventRecord, true, true)
    });
    style.top = DomHelper.percentify(top);
    style.height = DomHelper.percentify(height);
    style.paddingBottom = DomHelper.setLength(eventSpacing);
    style.width = "100%";
  }
};
__publicField(DayResourceZone, "$name", "DayResourceZone");
__publicField(DayResourceZone, "configurable", {
  allDayZoneClass: ResourceCalendarRowZone
});
var MonthZone = class extends Zone {
  static get configurable() {
    return {
      coverage: {
        $config: {
          equal: ObjectHelper.isEqual
        },
        value: null
      },
      hoverable: true,
      hoverEdges: "lr",
      hoverSelector: ".b-cal-event-wrap.b-allday"
    };
  }
  findRootElement(view) {
    return view.weeksElement;
  }
  // Drag handling
  captureDragOffset(eventRecord, hit, drag) {
    drag.set("eventOffset", [
      Math.floor(DateHelper.diff(this.clearTime(eventRecord.startDate), hit.date, "d")),
      "d"
    ]);
  }
  cleanupDrag() {
    super.cleanupDrag();
    const cleanDom = () => this.coverage = null;
    if (this.isCreating) {
      this.view.afterRefresh(cleanDom);
    } else {
      cleanDom();
    }
  }
  // Drop handling
  cleanupDrop() {
    var _a5, _b2;
    super.cleanupDrop();
    (_b2 = (_a5 = this.owner.client) == null ? void 0 : _a5._scrollManager) == null ? void 0 : _b2.stopMonitoring();
    const cleanDom = () => this.coverage = null;
    if (this.isCreating) {
      this.view.afterRefresh(cleanDom);
    } else {
      cleanDom();
    }
  }
  dropHitCreate(drag, hit) {
    const me = this, recurring = drag.ctrlKey, dragFrom = drag.peek("eventCreate");
    let recurrenceRule = null, count, day1, day2, endDate, startDate, week1, week2;
    endDate = me.clearTime(hit.date);
    startDate = me.clearTime(dragFrom.date);
    day1 = dragFrom.dayNumber;
    day2 = hit.dayNumber;
    week1 = dragFrom.weekOffset;
    week2 = hit.weekOffset;
    if (week2 < week1) {
      [week1, week2] = [week2, week1];
    }
    if (recurring) {
      count = week2 - week1 + 1;
      recurrenceRule = count > 1 ? `FREQ=WEEKLY;COUNT=${count}` : null;
      if (endDate < startDate) {
        startDate = endDate;
        if (day1 < day2) {
          startDate = DateHelper.add(startDate, day1 - day2, "d");
        }
      } else if (day2 < day1) {
        startDate = DateHelper.add(startDate, day2 - day1, "d");
      }
      if (day2 < day1) {
        [day1, day2] = [day2, day1];
      }
    } else if (endDate < startDate) {
      [startDate, endDate] = [endDate, startDate];
      [day1, day2] = [day2, day1];
    }
    me.setEventData({
      startDate,
      duration: DateHelper.as(me.eventRecord.durationUnit, (recurring ? day2 - day1 : DateHelper.diff(startDate, endDate, "d")) + 1, "d"),
      recurrenceRule
    });
  }
  dropHitNowhere(drag) {
    super.dropHitNowhere(drag);
    this.coverage = null;
  }
  // Misc
  setEventData(data, creating) {
    super.setEventData(data, creating);
    const { dropping, eventRecord, view } = this, { visibleCellSelector } = view, weekEls = DomHelper.children(view.weeksElement, "> .b-calendar-week"), coverage = {
      // weekNumber : String[] describing the days for a weekEl (in order of weekEls)
    }, add = (event) => {
      const { startDate, endDate } = event;
      for (let cells, cover, k, n, i = 0; i < weekEls.length; ++i) {
        cells = DomHelper.children(weekEls[i], visibleCellSelector);
        n = cells.length;
        cover = "";
        for (k = 0; k < n; ++k) {
          const dayStart = view.getDateFromElement(cells[k]), dayEnd = DateHelper.add(dayStart, 1, "d");
          if (startDate < dayEnd && dayStart < endDate) {
            if (!k && startDate < dayStart) {
              cover = "<";
            }
            cover += k;
            if (k === n - 1 && dayEnd < endDate) {
              cover += ">";
            }
          }
        }
        if (cover) {
          (coverage[i] || (coverage[i] = [])).push(cover);
        }
      }
    };
    if ((dropping == null ? void 0 : dropping.has("eventRecord")) || !eventRecord.recurrence) {
      add(eventRecord);
    } else {
      eventRecord.recurrence.forEachOccurrence(view.startDate, view.endDate, add);
    }
    this.coverage = coverage;
  }
  // Configs
  updateCoverage(coverage) {
    var _a5;
    const me = this, {
      dragging,
      weekValues,
      view
    } = me, { rtl } = view, {
      visibleCellSelector
    } = view, eventSourceHit = coverage && (dragging == null ? void 0 : dragging.peek("eventSourceHit")), eventTop = eventSourceHit == null ? void 0 : eventSourceHit.eventTop, newWeekValues = {}, eventRow = view.getWeekElementFor(eventSourceHit == null ? void 0 : eventSourceHit.eventElement), { weekElements } = view;
    let cell, cells, cov, cover, el, eventEl, extL, extR, i, k, weekEl;
    me.weekValues = newWeekValues;
    for (i = 0; i < weekElements.length; ++i) {
      if (!(cover = coverage == null ? void 0 : coverage[i])) {
        continue;
      }
      weekEl = weekElements[i];
      cells = DomHelper.children(weekEl, visibleCellSelector);
      for (k = 0; k < cover.length; ++k) {
        if (!(eventEl = (_a5 = weekValues == null ? void 0 : weekValues[i]) == null ? void 0 : _a5.shift())) {
          eventEl = DomHelper.createElement(me.eventDom);
        }
        (newWeekValues[i] || (newWeekValues[i] = [])).push(eventEl);
        cov = cover[k];
        extL = cov.includes("<") ? 1 : 0;
        extR = cov.includes(">") ? 1 : 0;
        cov = cov.substr(extL, cov.length - extR - extL);
        eventEl.classList[extL ? "add" : "remove"]("b-continues-past");
        eventEl.classList[extR ? "add" : "remove"]("b-continues-future");
        eventEl.style[rtl ? "right" : "left"] = DomHelper.percentify(100 * Number(cov[0]) / cells.length);
        eventEl.style.width = DomHelper.percentify(100 * cov.length / cells.length);
        if (eventTop && weekEl === eventRow) {
          eventEl.style.top = eventTop;
        }
        cell = cells[Number(cov[cov.length - 1])];
        el = DomHelper.down(cell, ".b-cal-event-bar-container");
        if (el !== eventEl.parentNode) {
          el.appendChild(eventEl);
        }
      }
    }
    if (weekValues) {
      for (i in weekValues) {
        weekValues[i].forEach((el2) => {
          if (el2.classList.contains("b-cal-tentative-event")) {
            el2.remove();
          }
        });
      }
    }
  }
};
__publicField(MonthZone, "$name", "MonthZone");
var OverflowZone = class extends Zone {
  static get configurable() {
    return {
      droppable: false,
      dragProxy: {
        type: "default",
        open(drag) {
          const me = this, { owner } = drag.source.view, sourceEl = drag.element.closest(".b-cal-event-wrap");
          if (owner.isYearView) {
            me.proxyEl = sourceEl.cloneNode(true);
            me.proxyEl.classList.add("b-cal-drag-proxy");
            me.proxyEl.style.width = `${sourceEl.offsetWidth}px`;
            me.proxyOffset = EventHelper.getClientPoint(drag.startEvent).getDelta(Rectangle.from(sourceEl));
            owner.contentElement.appendChild(me.proxyEl);
          }
        },
        dragMove(drag) {
          if (this.proxyEl) {
            DomHelper.alignTo(this.proxyEl, EventHelper.getClientPoint(drag.event).translate(15, 15), {
              align: "t0-t0"
            });
          }
        },
        close() {
          var _a5;
          (_a5 = this.proxyEl) == null ? void 0 : _a5.remove();
        }
      }
    };
  }
  findRootElement(view) {
    return view.contentElement;
  }
  beforeDrag(drag) {
    const hit = this.hitTest(drag);
    if ((hit == null ? void 0 : hit.type) !== "event" || !this.owner.draggable || !hit.eventRecord.isDraggable) {
      return false;
    }
    drag.set("eventRecord", hit.eventRecord);
    drag.set("eventDragMode", drag[eventDragSym] = modeDescriptor.move.mode);
  }
  dragStart() {
    this.view.hide();
  }
};
__publicField(OverflowZone, "$name", "OverflowZone");
var YearZone = class extends Zone {
  startCreate() {
    var _a5;
    (_a5 = this.view._overflowPopup) == null ? void 0 : _a5.hide();
    super.startCreate(...arguments);
  }
  // Drop handling
  dragEnter(drag) {
    const result = super.dragEnter(drag);
    if (result !== false) {
      this.view.contentElement.classList.add(this.draggingCls);
    }
    return result;
  }
  dragLeave(drag) {
    super.dragLeave(drag);
    this.view.contentElement.classList.remove(this.draggingCls);
  }
  dropHitCreate(drag, hit, dragFrom) {
    const me = this;
    let endDate = me.clearTime(hit.date), startDate = me.clearTime(dragFrom.date);
    if (endDate < startDate) {
      [startDate, endDate] = [endDate, startDate];
    }
    me.setEventData({
      startDate,
      endDate: DateHelper.add(endDate, 1, "d")
    });
    me.days = me.makeDayRange(startDate, endDate);
  }
  dropHitMove(drag, hit, eventRecord) {
    super.dropHitMove(drag, hit, eventRecord);
    const me = this, tempRec = me.eventRecord;
    let { endDate } = tempRec;
    if (tempRec.allDay) {
      endDate = DateHelper.add(endDate, -1, "d");
    }
    me.days = me.makeDayRange(tempRec.startDate, endDate);
  }
  // Misc
  includeDay(date2) {
    const els = DomHelper.children(this.view.bodyElement, `[data-date='${date2}']`);
    els.forEach((e) => e.classList.add(`b-cal-tentative-event${this.view.hideNonWorkingDays ? ":not(.b-nonworking-day)" : ""}`));
    return els;
  }
  makeDayRange(startDate, endDate) {
    const days2 = [];
    for (let date2 = startDate; date2 <= endDate; date2 = DateHelper.add(date2, 1, "d")) {
      days2.push(DateHelper.format(date2, YYYY_MM_DD));
    }
    return days2;
  }
  removeDay(date2, els) {
    els.forEach((e) => e.classList.remove("b-cal-tentative-event"));
  }
};
__publicField(YearZone, "$name", "YearZone");
var ResourceViewZone = class extends Base {
  static get configurable() {
    return {
      view: null,
      zones: {
        $config: ["nullify"],
        value: []
      }
    };
  }
  updateView(view) {
    view.eachView((view2) => {
      this.onResourceViewViewCreate({ view: view2 });
    });
    view.ion({
      viewCreate: "onResourceViewViewCreate",
      thisObj: this
    });
  }
  onResourceViewViewCreate({ view }) {
    const me = this, {
      zones,
      owner
    } = me, modes = owner.client.constructor.Modes, type = owner.getViewZoneType(modes.resolveType(view.type));
    type && zones.push(owner.createZone(type, {
      view,
      resource: view.defaultCalendar
    }));
  }
  changeZones(zones, oldZones) {
    if ((oldZones == null ? void 0 : oldZones.length) && !zones) {
      for (let i = 0, { length } = oldZones; i < length; i++) {
        oldZones[i].destroy();
      }
    }
    return zones;
  }
};
var CalendarDrag = class extends CalendarFeature {
  constructor() {
    super(...arguments);
    __publicField(this, "callOnFunctions", true);
  }
  static get configurable() {
    return {
      disableOnReadOnly: true,
      localizableProperties: [
        "newName",
        "recurrenceTip"
      ],
      /**
       * Specify `false` to disallow creating events by drag gestures.
       * @config {Boolean}
       */
      creatable: true,
      /**
       * Specify `false` to disallow dragging events to new times or days.
       * @config {Boolean}
       */
      draggable: true,
      /**
       * The {@link Scheduler.model.EventModel#field-durationUnit} to use when drag-creating events.
       *
       * If not specified, the `dragUnit` property of the active view's
       * {@link Calendar.widget.mixin.CalendarMixin#property-autoCreate} is used.
       *
       * For {@link Calendar.widget.DayView}s, this is normally `'hour'`, for views with a granularity
       * level of one day, the default is `'day'`.
       * @config {String}
       */
      durationUnit: null,
      /**
       * A {@link Core.helper.DomHelper#typedef-DomConfig DOM config} object used to create an extra element
       * during event drag to contain the end time of the tentative event. This element contains the CSS class
       * `'b-cal-event-footer'` which can be used for styling.
       *
       * Set this to `null` to remove the end time rendering during drag operations.
       * @config {DomConfig}
       * @default
       */
      footer: {
        className: "b-cal-event-footer"
      },
      /**
       * This is configured as a {@link Core.helper.DomHelper#function-createElement-static DomHelper}
       * specification and is promoted to an `HTMLElement` during initialization. This element is moved between
       * calendar event elements on hover in order to show drag handles on the event under the mouse.
       * @config {HTMLElement|DomConfig}
       * @private
       */
      gripper: {
        class: "b-gripper"
      },
      /**
       * The name of new events or a function to call with the event record that will return the event name.
       *
       * If a function is supplied, it is called in the scope of (the `this` reference is set to) the
       * view being dragged within.
       *
       * Cpnfigure as `null` to use the dragged-in view's {@link Calendar.widget.mixin.CalendarMixin#config-autoCreate}
       * `newName` property.
       * @config {String|Function}
       * @param {Scheduler.model.EventModel} eventRecord The record being drag-created.
       * @returns {String} Name of new event
       */
      newName: "L{newEvent}",
      /**
       * The text to display as a hint for creating recurring events during drag. This tip is displayed in the
       * {@link #config-tooltip} in the same place as the recurrence summary (when there is no recurrence to
       * display).
       * @config {String}
       */
      recurrenceTip: "(L{holdCtrlForRecurrence})",
      /**
       * Specify `false` to disallow dragging the edges of events to change their start or end.
       * @config {Boolean}
       */
      resizable: true,
      /**
       * The tooltip to display during a drag create process. Disabled by
       * default, set to `true`, or provide a tooltip / config object, to enable it.
       * @config {Boolean|EventTipConfig|Calendar.widget.EventTip}
       */
      tooltip: {
        $config: ["lazy", "nullify"],
        value: {
          type: "eventTip",
          disabled: true,
          forSelector: null,
          tools: null
        }
      },
      /**
       * An empty function by default that allows you to perform custom validation on an event being created by
       * a drag gesture.
       *
       * The `drag` context contains the following data items (see {@link Core.util.drag.DragContext#function-get}):
       *
       *  - `eventDragMode` : The {@link #typedef-CalendarDragMode} object describing the drag operation.
       *  - `eventCreate` : The {@link Calendar.view.Calendar#typedef-CalendarHit} object that describes the target of the drag operation.
       *
       * Return `false` to cancel the create operation.
       *
       * This function can return a `Promise` (i.e., it can be `async`).
       *
       * Example:
       * ```javascript
       *  let calendar = new Calendar({
       *      features : {
       *          drag : {
       *              async validateCreateFn({ eventRecord, drag }) {
       *                  // This method can be async so it can make ajax requests or interact
       *                  // with the user...
       *
       *                  // if we return false, the event will be discarded
       *
       *                  // The following is equivalent to returning false:
       *                  //
       *                  // return {
       *                  //     // Do not add the event to the store
       *                  //     add  : false,
       *                  //     // Do not display the edit dialog (in the eventEdit feature):
       *                  //     edit : false
       *                  // };
       *                  //
       *                  // This simply adds the event and does not display the editor:
       *                  //
       *                  return {
       *                      edit : false
       *                  };
       *
       *                  // To do delay adding the event until the editor is done (and
       *                  // not via Cancel):
       *                  // return {
       *                  //     add : false
       *                  // };
       *              }
       *          }
       *      }
       *  });
       * ```
       * or:
       * ```javascript
       *  let calendar = new Calendar({
       *      features : {
       *          drag : {
       *              // Will resolve on the Calendar
       *              validateCreateFn : 'up.validateCreate'
       *          }
       *      },
       *      validateCreate{ eventRecord, drag } {
       *          ...
       *      }
       *  });
       * ```
       *
       * Return `true` if the event should be added to the event store and to inform the
       * {@link Calendar.feature.EventEdit eventEdit} feature to display the edit dialog.
       *
       * If this function returns an object, the `add` property can be set to `false`
       * to prevent adding to the event store, and the `edit` property can be set to `false` to inform the
       * `eventEdit` feature not to display the edit dialog.
       *
       * @config {Function|String}
       * @param {Object} info
       * @param {Core.util.drag.DragContext} info.drag The drag create context.
       * @param {Event} info.event The browser event object.
       * @param {Scheduler.model.EventModel} info.eventRecord The Event record.
       * @returns {Boolean|ValidateCreateResult} Return `false` if this event should be rejected.
       */
      validateCreateFn: () => {
      },
      /**
       * An empty function by default that allows you to perform custom validation on the event being moved to a
       * new date or time via a drag gesture.
       *
       * The `drag` context contains the following data items (see {@link Core.util.drag.DragContext#function-get}):
       *
       *  - `eventDragMode` : The {@link #typedef-CalendarDragMode} object describing the drag operation.
       *  - `eventRecord` : The {@link Scheduler.model.EventModel event record} being moved.
       *  - `eventSourceHit` : The {@link Calendar.view.Calendar#typedef-CalendarHit} object that describes the source of the drag operation.
       *
       * Return `false` to cancel the operation.
       *
       * This function can return a `Promise` (i.e., it can be `async`).
       *
       * Example:
       * ```javascript
       *  let calendar = new Calendar({
       *      features : {
       *          drag : {
       *              async validateMoveFn({ eventRecord, drag }) {
       *                  // This method can be async so it can make ajax requests or interact
       *                  // with the user...
       *
       *                  // if we return false, the event move will be discarded
       *              }
       *          }
       *      }
       *  });
       * ```
       * or:
       * ```javascript
       *  let calendar = new Calendar({
       *      features : {
       *          drag : {
       *              // Will resolve on the Calendar
       *              validateMoveFn : 'up.validateMove'
       *          }
       *      },
       *      validateMove{ eventRecord, drag } {
       *          ...
       *      }
       *  });
       * ```
       *
       * @config {Function|String}
       * @param {Object} info
       * @param {Core.util.drag.DragContext} info.drag The drag create context.
       * @param {Event} info.event The browser event object.
       * @param {Scheduler.model.EventModel} info.eventRecord The Event record.
       * @returns {Boolean} Return `false` if this event change should be rejected.
       */
      validateMoveFn: () => {
      },
      /**
       * An empty function by default that allows you to perform custom validation on the event whose `startDate`
       * or `endDate` is being modified via drag gesture.
       *
       * The `drag` context contains the following data items (see {@link Core.util.drag.DragContext#function-get}):
       *
       *  - `eventDragMode` : The {@link #typedef-CalendarDragMode} object describing the drag operation.
       *  - `eventSourceHit` : The {@link Calendar.view.Calendar#typedef-CalendarHit} object that describes the source of the drag operation.
       *
       * Return `false` to cancel the operation.
       *
       * This function can return a `Promise` (i.e., it can be `async`).
       *
       * Example:
       * ```javascript
       *  let calendar = new Calendar({
       *      features : {
       *          drag : {
       *              async validateResizeFn({ eventRecord, drag }) {
       *                  // This method can be async so it can make ajax requests or interact
       *                  // with the user...
       *
       *                  // if we return false, the event change will be discarded
       *              }
       *          }
       *      }
       *  });
       * ```
       * or:
       * ```javascript
       *  let calendar = new Calendar({
       *      features : {
       *          drag : {
       *              // Will resolve on the Calendar
       *              validateResizeFn : 'up.validateResize'
       *          }
       *      },
       *      validateResize{ eventRecord, drag } {
       *          ...
       *      }
       *  });
       * ```
       *
       * @config {Function|String}
       * @param {Object} info
       * @param {Core.util.drag.DragContext} info.drag The drag create context.
       * @param {Event} info.event The browser event object.
       * @param {Scheduler.model.EventModel} info.eventRecord The Event record.
       * @returns {Boolean|Promise} Return `false` if this event change should be rejected.
       */
      validateResizeFn: () => {
      },
      zoneTypes: {
        day: DayZone,
        // also covers WeekView
        dayagenda: DayAgendaZone,
        month: MonthZone,
        year: YearZone,
        resource: ResourceViewZone,
        dayresource: DayResourceZone
        // AgendaView is not supported (though it could be a Draggable just not a Droppable)
      },
      /**
       * By default, when an event is dragged from an external source, the event is removed from the
       * source EventStore. Configure this as `false` to leave the event in place to allow for the dragging
       * in of the same event repeatedly.
       * @prp {Boolean}
       * @default
       */
      removeFromExternalStore: true
    };
  }
  // Called as a callOnFunctions function by the firing of the beforeDragStart event.
  // The beforeAutoCreate event is also triggered by CalendarMixin's detection of its own
  // autoCreate gesture. This event gives a common point for validation of UI-initiated
  // event creation.
  onBeforeDragStart(props) {
    var _a5;
    const { client } = this, {
      drag,
      event: domEvent
    } = props, { view } = drag.source, operation = drag[eventDragSym].type, resourceRecord = (_a5 = view.getResourceRecord) == null ? void 0 : _a5.call(view, drag.element), date2 = drag.peek("date"), event = {
      drag,
      domEvent,
      view,
      date: date2,
      resourceRecord,
      eventRecord: drag.peek("eventRecord"),
      feature: this.owner
    };
    if (client.trigger(`beforeDrag${StringHelper.capitalize(operation)}`, event) === false) {
      return false;
    }
    if (drag.peek("eventDragMode").type === "create") {
      return client.trigger("beforeAutoCreate", { view, domEvent, date: date2, resourceRecord });
    }
  }
  changeGripper(gripper, was) {
    was == null ? void 0 : was.remove();
    return gripper && DomHelper.createElement(gripper);
  }
  changeTooltip(config, existing) {
    if (config) {
      config = config === true ? this.constructor.configurable.tooltip.value : config;
      if (this.initialConfig.tooltip) {
        config.disabled = false;
      }
      config.ownerFeature = this;
    }
    return Widget.reconfigure(
      existing,
      config,
      /* owner = */
      this
    );
  }
};
__publicField(CalendarDrag, "$name", "CalendarDrag");
__publicField(CalendarDrag, "type", "drag");
CalendarDrag.initClass();
CalendarDrag._$name = "CalendarDrag";

// lib/Calendar/feature/EventBuffer.js
var wrapFieldDefinitions = {
  wrapStartDate: {
    get() {
      return DateHelper.add(this.startDate, Duration.from(this.preamble).negate());
    }
  },
  wrapEndDate: {
    get() {
      return DateHelper.add(this.endDate, Duration.from(this.postamble));
    }
  },
  outerStartDate: {
    get() {
      return this.wrapStartDate;
    }
  },
  outerEndDate: {
    get() {
      return this.wrapEndDate;
    }
  }
};
var EventBuffer = class extends CalendarFeature {
  static get pluginConfig() {
    return {
      chain: ["onEventDataGenerated"]
    };
  }
  onEventDataGenerated({ eventRecord, resourceRecord, view }, { children }) {
    var _a5, _b2;
    if (!Object.getOwnPropertyDescriptor(eventRecord.constructor.prototype, "wrapStartDate")) {
      Object.defineProperties(eventRecord.constructor.prototype, wrapFieldDefinitions);
    }
    if (this.enabled && view.isDayView) {
      const {
        showDuration,
        renderer
      } = this, preambleConfig = {}, postambleConfig = {};
      let preamble = Duration.from(eventRecord.preamble), postamble = Duration.from(eventRecord.postamble);
      renderer && view.callback(renderer, view, [{ eventRecord, resourceRecord, preambleConfig, postambleConfig }]);
      if (!(preamble == null ? void 0 : preamble.magnitude)) {
        preamble = null;
      }
      if (!(postamble == null ? void 0 : postamble.magnitude)) {
        postamble = null;
      }
      const preambleMinutes = preamble ? DateHelper.as("minute", preamble) : 0, postambleMinutes = postamble ? DateHelper.as("minute", postamble) : 0;
      if (preamble) {
        const domConfig = {
          className: {
            "b-cal-event-buffer": 1,
            "b-cal-preamble": 1,
            [preambleConfig.cls]: preambleConfig.cls
          },
          style: {
            height: `calc(var(--hour-height) * ${preambleMinutes / 60} + 1px)`
          },
          children: showDuration ? [{
            className: "b-travel-time-label",
            children: [
              preambleConfig.icon ? {
                tag: "i",
                className: preambleConfig.icon
              } : void 0,
              {
                tag: "span",
                className: "b-buffer-label",
                text: (_a5 = preambleConfig.text) != null ? _a5 : preamble.toString()
              }
            ]
          }] : void 0
        };
        if (showDuration && preambleConfig.tooltip) {
          domConfig.children[0].dataset = { btip: preambleConfig.tooltip };
        }
        children.push(domConfig);
      }
      if (postamble) {
        const domConfig = {
          className: {
            "b-cal-event-buffer": 1,
            "b-cal-postamble": 1,
            [postambleConfig.cls]: postambleConfig.cls
          },
          style: {
            height: `calc(var(--hour-height) * ${postambleMinutes / 60} + 1px)`
          },
          children: showDuration ? [{
            className: "b-travel-time-label",
            children: [
              postambleConfig.icon ? {
                tag: "i",
                className: postambleConfig.icon
              } : void 0,
              {
                tag: "span",
                className: "b-buffer-label",
                text: (_b2 = postambleConfig.text) != null ? _b2 : postamble.toString()
              }
            ]
          }] : void 0
        };
        if (showDuration && postambleConfig.tooltip) {
          domConfig.children[0].dataset = { btip: postambleConfig.tooltip };
        }
        children.push(domConfig);
      }
    }
  }
  updateShowDuration() {
    if (!this.isConfiguring) {
      this.client.eachView("refresh");
    }
  }
  doDisable() {
    if (!this.isConfiguring) {
      this.client.eachView("refresh");
    }
  }
};
__publicField(EventBuffer, "$name", "EventBuffer");
__publicField(EventBuffer, "type", "eventBuffer");
__publicField(EventBuffer, "configurable", {
  /**
   * Show buffer duration labels
   * @config {Boolean}
   * @default
   */
  showDuration: true,
  /**
   * A function which lets you customize the visual appearance of the preamble and postamble elements.
   *
   * <div class="note">
   * You should never modify any records inside this method.
   * </div>
   *
   * @config {Function}
   * @param {Object} data
   * @param {Scheduler.model.EventModel} data.eventRecord The event record
   * @param {Scheduler.model.ResourceModel} data.resourceRecord The resource record (only available in resource views)
   * @param {Object} data.preambleConfig A config object for the preamble
   * @param {String} data.preambleConfig.icon A CSS class for the icon to show (e.g. car / bicycle)
   * @param {Object|String} data.preambleConfig.cls A CSS object for the preamble element
   * @param {String} data.preambleConfig.text The text to display
   * @param {String} data.preambleConfig.tooltip Extra information to display when hovering the `text`
   * @param {Object} data.postambleConfig A config object for the postamble
   * @param {String} data.postambleConfig.icon A CSS class for the icon to show (e.g. car / bicycle)
   * @param {Object|String} data.postambleConfig.cls A CSS object for the postamble element
   * @param {String} data.postambleConfig.text The text to display
   * @param {String} data.postambleConfig.tooltip Extra information to display when hovering the `text`
   * @returns {void}
   */
  renderer: null
});
CalendarFeature.register(EventBuffer.type, EventBuffer);
EventBuffer._$name = "EventBuffer";

// lib/Calendar/feature/ExternalEventSource.js
var oneHour = {
  magnitude: 1,
  unit: "hour"
};
var ExternalZone = class extends Base.mixin(Draggable_default, Droppable_default) {
  static get configurable() {
    return {
      // Default to dragging grid rows
      dragItemSelector: ".b-grid-row",
      dragProxy: {
        type: "default",
        open(drag) {
          const feature = this.owner.owner, proxyEl = feature.openProxy(drag);
          drag.cleaners.push(() => {
            feature.insertionPoint = null;
            proxyEl.remove();
          });
          return proxyEl;
        },
        dragMove(drag) {
          return this.owner.owner.moveProxy(drag);
        },
        close() {
          var _a5;
          (_a5 = this.owner.owner.proxyEl) == null ? void 0 : _a5.remove();
        }
      }
    };
  }
  beforeDrag(drag) {
    return this.owner.onDragStart(drag);
  }
  dragEnter(drag) {
    if (drag.source.isMoving) {
      drag.element = drag.targetElement;
      this.dragProxy.open(drag);
    }
  }
  dragMove(drag) {
    if (drag.source.isMoving) {
      this.dragProxy.dragMove(drag);
    }
    if (drag.source.isMoving || drag.source.isExternalZone) {
      return this.owner.onDragMove(drag);
    }
  }
  dragDrop(drag) {
    if (drag.source.isMoving) {
      this.dragProxy.close();
    }
    if (drag.target === this && (drag.source.isMoving || drag.source.isExternalZone)) {
      return this.owner.onDragDrop(drag);
    }
  }
};
__publicField(ExternalZone, "$name", "ExternalZone");
var _ExternalEventSource = class _ExternalEventSource extends CalendarFeature.mixin(Delayable_default) {
  static get delayable() {
    return {
      onCalendarPaint: "raf"
    };
  }
  static get configurable() {
    return {
      /**
       * The grid, or `id` of a grid from which events are to be dragged.
       * @config {Grid.view.Grid|String}
       */
      grid: {
        $config: ["lazy"],
        value: null
      },
      /**
       * If not dragging from a grid, which is the default mode, then an element from which
       * dragging can take place must be supplied in the `dragRootElement` config.
       *
       * May also be specified as a selector which matches a unique element, or a simple element id.
       *
       * In this case a {@link #config-dragItemSelector} string, and {@link #config-getRecordFromElement}
       * function must be supplied to allow event records to be sourced from the element, for example:
       *
       * ```javascript
       * features : {
       *     externalEventSource : {
       *         dragRootElement  : '#mySourceElementId',
       *         dragItemSelector : '.my-item-class'
       *     }
       * }
       * ```
       * @config {HTMLElement|String}
       */
      dragRootElement: {
        $config: "lazy",
        value: null
      },
      /**
       * If not dragging from a grid, which is the default mode, then a selector which identifies
       * draggable elements within the {@link #config-dragRootElement}.
       *
       * In the simplest case, the identified element may contain simply a string which is used
       * as the event name, for example:
       *
       * ```javascript
       * features : {
       *     externalEventSource : {
       *         dragRootElement  : '#mySourceElementId',
       *         dragItemSelector : '.my-item-class'
       *     }
       * }
       * ```
       * @config {String}
       */
      dragItemSelector: null,
      /**
       * If not dragging from a grid, which is the default mode, then a function which returns
       * an event record to drag from a passed element must be supplied.
       *
       * In this case a {@link #config-dragRootElement} and a {@link #config-dragItemSelector} string
       * may be supplied to allow event records to be sourced from the element.
       *
       * If the element identified by the {@link #config-dragItemSelector} just contains an event
       * name to create, this configuration is optional. A new event will be created by that name, for example:
       *
       * ```javascript
       * features : {
       *     externalEventSource : {
       *         dragRootElement  : '#mySourceElementId',
       *         dragItemSelector : '.my-item-class',
       *         getRecordFromElement(element) {
       *             // Return an object from which an EventModel can be created.
       *             // Same format as loading an EventStore. { name : 'name', startDate: ''} etc
       *             return myController.createRecordFromElement(element);
       *         }
       *     }
       * }
       * ```
       *
       * @config {Function|String}
       * @param {HTMLElement} element HTML element
       * @returns {Scheduler.model.EventModel|null}
       */
      getRecordFromElement: function(element) {
        const grid = this.grid || (this.grid = Widget.fromElement(element, "grid"));
        if (grid) {
          return grid.getRecordFromElement(element);
        }
        return element.innerText;
      },
      /**
       * By default, the proxy shown when "picking up" the grid row is hidden
       * when dragging over the calendar because the {@link Calendar.feature.CalendarDrag}
       * feature automatically shows a drop position indicator which shows where the
       * proposed new event will be.
       * @config {Boolean}
       * @default
       */
      hideExternalProxy: true,
      /**
       * An object which overrides or augments the default configuration for the
       * {@link Core.mixin.Draggable} which handles picking up events.
       *
       * This is only necessary if there is no {@link #config-grid} specified.
       * @config {Object} [draggable]
       * @default
       */
      draggable: {
        $config: ["lazy"],
        value: {}
      },
      /**
       * An object which, if present, causes creation of a {@link Core.mixin.Droppable} which
       * handles dropping events *from* the Calendar into the external location.
       *
       * In the simplest configuration, configure this as `true`.
       *
       * __Important:__ when dropping on an external source, the record is _not_ removed
       * from the Calendar's `eventStore`. You will need to listen for the {@link #event-beforeDropExternal} event.
       * @config {Object|Boolean} [droppable]
       * @default
       */
      droppable: {
        $config: ["lazy"],
        value: null
      },
      /**
       * Used when {@link #config-droppable} is used, and the external source is a Grid. This
       * allows us to track the over row to highlight the insertion point.
       * @private
       */
      insertionPoint: null
    };
  }
  construct() {
    super.construct(...arguments);
    if (this.client.isPainted) {
      this.onCalendarPaint({ source: this.client, firstPaint: true });
    }
  }
  // This is deferred to the next AF to allow the configured grid ID to be available
  onCalendarPaint({ source: calendar, firstPaint }) {
    if (firstPaint) {
      const {
        draggable = {},
        droppable = {}
      } = this, zone = new ExternalZone({
        view: this.grid,
        ...draggable,
        ...droppable
      });
      if (this._draggable) {
        this._draggable = zone;
      }
      if (this._droppable) {
        this._droppable = zone;
      }
    }
    calendar.ion({
      beforeDragMoveEnd: "onBeforeDragMoveEnd",
      dragMoveEnd: "onDragMoveEnd",
      thisObj: this
    });
  }
  onBeforeDragMoveEnd({ drag }) {
    var _a5;
    if (drag.source === this.draggable) {
      drag.dropOnCalendar = true;
      const gridStore = (_a5 = this.grid) == null ? void 0 : _a5.store, result = this.client.trigger("beforeDropExternal", drag);
      (gridStore == null ? void 0 : gridStore.isChained) && gridStore.fillFromMaster();
      return result;
    }
  }
  onDragMoveEnd({ drag }) {
    if (drag.source === this.draggable) {
      return this.client.trigger("dropExternal", drag);
    }
  }
  onDragStart(drag) {
    var _a5;
    const me = this, {
      client,
      grid
    } = me, { eventStore } = client, { modelClass } = eventStore;
    if (!drag.itemElement) {
      return false;
    }
    let eventRecord = me.callback(me.getRecordFromElement, me, [drag.itemElement]);
    if (eventRecord.isModel) {
      if (grid) {
        drag.set("sourceStore", grid.store);
      }
      if (!eventRecord.isEventModel) {
        eventRecord = eventStore.createRecord(eventRecord.data);
      }
    } else {
      if (typeof eventRecord === "string") {
        const autoCreate = client.activeView.autoCreate || client.autoCreate, duration = DateHelper.parseDuration((autoCreate == null ? void 0 : autoCreate.duration) || oneHour);
        eventRecord = {
          [modelClass.getFieldDataSource("name")]: eventRecord,
          [modelClass.getFieldDataSource("duration")]: duration.magnitude,
          [modelClass.getFieldDataSource("durationUnit")]: duration.unit
        };
      }
      eventRecord = eventStore.createRecord(eventRecord);
    }
    drag.set("sourceStore", (_a5 = me.grid) == null ? void 0 : _a5.store);
    drag.set("eventRecord", eventRecord);
    drag.eventRecord = eventRecord;
  }
  onDragMove(drag) {
    const { grid } = this;
    if (grid) {
      const row = grid.rowManager.getRowFor(drag.targetElement), record = row && grid.store.getAt(row.index);
      if (row) {
        const isAbove = drag.lastMoveEvent.clientY < row.getRectangle(Object.keys(grid.subGrids)[0]).center.y;
        drag.overIndex = row.index;
        drag.overRecord = record;
        drag.isAbove = isAbove;
        this.insertionPoint = record === drag.peek("eventRecord") ? {} : { row, isAbove };
      } else if (grid.contains(drag.targetElement)) {
        const hcCls = grid.headerContainer.classList, { bottomRow } = grid.rowManager;
        drag.overIndex = (bottomRow == null ? void 0 : bottomRow.index) || -1;
        drag.overRecord = bottomRow ? grid.store.getById(bottomRow.id) : null;
        drag.isAbove = false;
        this.insertionPoint = {
          row: bottomRow || {
            addCls: (c) => hcCls.add(c),
            removeCls: (c) => hcCls.remove(c)
          }
        };
      } else {
        drag.overIndex = NaN;
        this.insertionPoint = null;
      }
    }
    drag.eventRecord = drag.data.get("eventRecord");
    this.client.trigger("dragMoveExternal", drag);
  }
  changeInsertionPoint(ip, was) {
    if ((ip == null ? void 0 : ip.row) !== (was == null ? void 0 : was.row) || (ip == null ? void 0 : ip.isAbove) !== (was == null ? void 0 : was.isAbove)) {
      return ip;
    }
  }
  updateInsertionPoint(ip, was) {
    var _a5, _b2;
    (_a5 = was == null ? void 0 : was.row) == null ? void 0 : _a5.removeCls(was.isAbove ? "b-drop-above" : "b-drop-below");
    (_b2 = ip == null ? void 0 : ip.row) == null ? void 0 : _b2.addCls(ip.isAbove ? "b-drop-above" : "b-drop-below");
  }
  async onDragDrop(drag) {
    const {
      client,
      grid
    } = this, { eventStore } = client, { eventRecord } = drag, gridStore = grid == null ? void 0 : grid.store, sameStore = gridStore === eventStore || (gridStore == null ? void 0 : gridStore.masterStore) === eventStore;
    if (client.trigger("beforeDropExternal", drag) === false) {
      (gridStore == null ? void 0 : gridStore.isChained) && gridStore.fillFromMaster();
      return;
    }
    if (!isNaN(drag.overIndex)) {
      if (eventStore.includes(eventRecord) && !sameStore && !drag.source.view.resource) {
        eventStore.remove(eventRecord);
      }
      if (sameStore) {
        eventStore.move(eventRecord, eventStore.getAt(drag.overIndex + drag.isAbove ? 1 : 0));
      } else {
        gridStore.insert(drag.overIndex + (drag.isAbove ? 0 : 1), eventRecord);
      }
      (gridStore == null ? void 0 : gridStore.isChained) && gridStore.fillFromMaster();
    }
    client.trigger("dropExternal", drag);
  }
  openProxy(drag) {
    var _a5;
    const {
      grid,
      dragRootElement,
      client
    } = this, sourceEl = drag.element.closest(this.draggable.dragItemSelector) || drag.element, nameCell = grid ? sourceEl.querySelector('[data-column="name"]') : sourceEl, proxyEl = this.proxyEl || (this.proxyEl = DomHelper.createElement({
      className: "b-grid-to-cal-drag-proxy"
    })), dragOuter = (grid == null ? void 0 : grid.element) || dragRootElement.parentNode, proxyParent = client.element.contains(dragOuter) ? client.element : dragOuter;
    proxyEl.innerHTML = ((_a5 = drag.peek("eventRecord")) == null ? void 0 : _a5.name) || (nameCell == null ? void 0 : nameCell.innerHTML) || this.owner.L("L{newEvent}");
    this.proxyOffset = EventHelper.getClientPoint(drag.event).getDelta(Rectangle.from(sourceEl));
    proxyParent.appendChild(proxyEl);
    return proxyEl;
  }
  moveProxy(drag) {
    var _a5;
    const { proxyEl } = this, { targetElement } = drag, { activeView } = this.client;
    if (proxyEl) {
      const isOverClient = activeView.eventContentElement.contains(targetElement) || ((_a5 = activeView.allDayEvents) == null ? void 0 : _a5.contains(targetElement));
      if (isOverClient && this.hideExternalProxy) {
        proxyEl.classList.add("b-hide-display");
      } else {
        proxyEl.classList.remove("b-hide-display");
        DomHelper.alignTo(proxyEl, EventHelper.getClientPoint(drag.event).translate(10, 10), {
          align: "t0-t0"
        });
      }
    }
  }
  changeDragRootElement(dragRootElement) {
    if (typeof dragRootElement === "string") {
      dragRootElement = document.querySelector(dragRootElement) || document.getElementById(dragRootElement);
    }
    return dragRootElement;
  }
  changeDraggable(draggable) {
    const { grid } = this, dragRootElement = (grid == null ? void 0 : grid.contentElement) || this.dragRootElement, { dragItemSelector } = this;
    draggable = _ExternalEventSource.mergeConfigs({
      owner: this,
      grid
    }, draggable);
    if (dragRootElement) {
      draggable.dragRootElement = dragRootElement;
    }
    if (dragItemSelector) {
      draggable.dragItemSelector = dragItemSelector;
    }
    return draggable;
  }
  changeDroppable(droppable) {
    const { grid } = this, dropRootElement = (grid == null ? void 0 : grid.contentElement) || this.dropRootElement;
    if (droppable) {
      droppable = _ExternalEventSource.mergeConfigs({
        owner: this,
        grid
      }, droppable);
      if (dropRootElement) {
        droppable.dropRootElement = dropRootElement;
      }
    }
    return droppable;
  }
  changeGrid(grid) {
    if (grid) {
      const type = typeof grid, {
        resourceStore,
        project
      } = this.client;
      if (type === "string") {
        grid = Widget.getById(grid);
      }
      grid.setConfig({
        resourceStore,
        project
      });
    }
    return grid;
  }
};
__publicField(_ExternalEventSource, "$name", "ExternalEventSource");
__publicField(_ExternalEventSource, "type", "externalEventSource");
var ExternalEventSource = _ExternalEventSource;
ExternalEventSource.initClass();
ExternalEventSource._$name = "ExternalEventSource";

// lib/Calendar/feature/experimental/ExcelExporter.js
var ExcelExporter3 = class extends ExcelExporter2 {
  constructor(config) {
    super(config.client, config);
  }
  get exporter() {
    const me = this;
    return me._exporter || (me._exporter = me.exporterClass.new({ target: me.client }, me.exporterConfig));
  }
};
__publicField(ExcelExporter3, "$name", "ExcelExporter");
__publicField(ExcelExporter3, "type", "excelExporter");
CalendarFeature.register(ExcelExporter3.type, ExcelExporter3);
ExcelExporter3._$name = "ExcelExporter";

// lib/Calendar/feature/LoadOnDemand.js
var LoadOnDemand = class extends CalendarFeature {
  construct(config) {
    const { client } = config;
    client.eventStore.syncDataOnLoad = false;
    client.ion({
      dateRangeRequested: "onClientDateRangeRequested",
      thisObj: this
    });
    client.crudManager.ion({
      beforeLoadApply: "onCrudManagerBeforeApply",
      thisObj: this,
      prio: 9999
    });
    super.construct(...arguments);
  }
  // Tests if the CrudManager has an outstanding load request which would satisfy the passed date range
  hasOutstandingLoadFor(startDate, endDate) {
    var _a5;
    const { load } = this.client.crudManager.activeRequests;
    if (load) {
      const otherDateRangeRequested = (_a5 = load.options) == null ? void 0 : _a5.dateRangeRequested;
      if (otherDateRangeRequested && DateHelper.timeSpanContains(
        otherDateRangeRequested.startDate,
        otherDateRangeRequested.endDate,
        startDate,
        endDate
      )) {
        return true;
      }
    }
  }
  // We observe our CrudManager's successful loads and register the date range requested.
  // This is so that when a client dateRangeChange event is encountered, we can only
  // trigger a load if the date range is not already loaded unless alwaysLoadNewRange is set..
  onCrudManagerBeforeApply({ response, options }) {
    this.lastRangeLoaded = response.success && (options == null ? void 0 : options.dateRangeRequested);
  }
  onClientDateRangeRequested({
    new: {
      startDate,
      endDate
    },
    changed
  }) {
    const me = this, { client } = me;
    if (!changed && me.alwaysLoadNewRange) {
      return;
    }
    const { lastRangeLoaded } = me;
    if (lastRangeLoaded && !me.alwaysLoadNewRange) {
      const {
        startDate: lastStartDate,
        endDate: lastEndDate
      } = me.lastRangeLoaded;
      if (DateHelper.timeSpanContains(lastStartDate, lastEndDate, startDate, endDate)) {
        return;
      }
    }
    if (changed) {
      DomHelper.addTemporaryClass(client.element, "b-notransition", 100, client);
      if (me.clearOnNewRange) {
        client.eventStore.clear(true);
        me.lastRangeLoaded = null;
      }
    }
    if (!me.disabled && !me.hasOutstandingLoadFor(startDate, endDate)) {
      me.loadDateRange(startDate, endDate);
    }
  }
  /**
   * Reloads the currently loaded date range.
   *
   * If your app detects that the data may be stale, or needs to periodically refresh the data,
   * this method may be used to issue a server request to reload the currently loaded date range.
   */
  refresh() {
    const { lastRangeLoaded } = this;
    if (lastRangeLoaded) {
      this.loadDateRange(lastRangeLoaded.startDate, lastRangeLoaded.endDate);
    }
  }
  loadDateRange(startDate, endDate) {
    const me = this, {
      pendingLoad,
      client
    } = me, { datePicker } = client;
    if (datePicker == null ? void 0 : datePicker.showEvents) {
      const { activeView } = client;
      startDate = new Date(Math.min(activeView.startDate, datePicker.startDate));
      endDate = new Date(Math.max(activeView.endDate, datePicker.endDate));
    }
    if (pendingLoad) {
      pendingLoad.startDate = DateHelper.min(startDate, pendingLoad.startDate);
      pendingLoad.endDate = DateHelper.max(endDate, pendingLoad.endDate);
    } else {
      me.pendingLoad = {
        startDate,
        endDate
      };
      me.client.requestAnimationFrame(() => me.load());
    }
  }
  async load() {
    const me = this, {
      client,
      beforeRequest,
      dateFormat
    } = me, {
      crudManager
    } = client, {
      load
    } = crudManager.activeRequests, {
      startDate,
      endDate
    } = me.pendingLoad;
    const options = {
      dateRangeRequested: me.pendingLoad,
      request: {
        params: {
          [me.startDateParamName]: DateHelper.format(startDate, dateFormat),
          [me.endDateParamName]: DateHelper.format(endDate, dateFormat)
        }
      }
    };
    me.pendingLoad = null;
    if (beforeRequest) {
      me.callback(beforeRequest, client, [options]);
    }
    if (load) {
      if (me.hasOutstandingLoadFor(startDate, endDate)) {
        return;
      }
      try {
        await crudManager.cancelRequest(load.desc, load.reject);
      } catch (e) {
      }
    }
    let result;
    try {
      result = await crudManager.load(options);
    } catch (e) {
      if (!client.isDestroyed && !e.cancelled && client.trigger("loadOnDemandFail", e) !== false) {
        MessageDialog_default.alert({
          title: client.L("L{Calendar.loadFail}"),
          message: e.message
        });
      }
    }
    result && !client.isDestroyed && client.trigger("dateRangeLoad", { response: result.response, options, startDate, endDate });
  }
};
__publicField(LoadOnDemand, "$name", "LoadOnDemand");
__publicField(LoadOnDemand, "type", "loadOnDemand");
__publicField(LoadOnDemand, "configurable", {
  /**
   * The name of the HTTP parameter which contains the start date of the view requiring new data.
   * @config {String}
   * @default
   */
  startDateParamName: "startDate",
  /**
   * The name of the HTTP parameter which contains the end date of the view requiring new data.
   * @config {String}
   * @default
   */
  endDateParamName: "endDate",
  /**
   * The {@link Core.helper.DateHelper#function-format-static DateHelper} format string to use to
   * encode the start date and end date of the events to load when the view requires a new date range.
   * @config {String}
   * @default
   */
  dateFormat: "YYYY-MM-DD",
  /**
   * A function, or name of a function in the ownership hierarchy which may be called
   * to mutate the `options` packet that is passed to the {@link Scheduler.data.CrudManager}
   * {@link Scheduler.crud.AbstractCrudManagerMixin#function-load} method.
   * One possible use of this function is to mutate the `options.request.params` object to add extra
   * parameters for the server.
   *
   * @config {Function|String}
   * @param {Object} options The `options` parameter to be sent to the {@link Scheduler.data.CrudManager}
   *   {@link Scheduler.crud.AbstractCrudManagerMixin#function-load} method.
   * @param {Object} options.dateRangeRequested An object containing the start and end dates of the range to load.
   * @param {Date} options.dateRangeRequested.startDate The start date of the range to request.
   * @param {Date} options.dateRangeRequested.endDate The end date of the range to request. **Note that Dates are timestamps**.
   * @param {Object} options.request A configuration object for the CrudManager load request
   * @param {Object} options.request.params An object where the property name is the HTTP parameter name and the property value is the parameter value.
   * @returns {void}
   */
  beforeRequest: null,
  /**
   * By default, if a view requests a date range that we have already loaded, no
   * network request is made, and the events will be loaded from the current content
   * of the event store.
   *
   * To make the feature load a new event block on every request for a __new__ date range,
   * configure this as `true`.
   * @config {Boolean}
   * @default false
   */
  alwaysLoadNewRange: null,
  /**
   * Configure this as `true` to clear the event store when a new date range has been requested
   * instead of leaving it until the load of the new data to correct the store contents,
   *
   * Setting this to true clears the event store prior to requesting the data load.
   * @config {Boolean}
   * @default false
   */
  clearOnNewRange: null
});
LoadOnDemand.initClass();
LoadOnDemand._$name = "LoadOnDemand";

// lib/Calendar/feature/print/printer/Printer.js
var Printer = class extends Exporter {
  restoreState({ client }) {
  }
  saveState({ client }) {
  }
  cloneElement(element, target = this.element, clear = true) {
    if (clear) {
      target.innerHTML = "";
    }
    target.appendChild(element.cloneNode(true));
  }
  /**
   * Template of an extracted page.
   * @param {Object} data Data for the page template
   * @returns {String}
   */
  pageTpl(data) {
    const {
      config,
      title,
      header,
      footer,
      styles: styles2,
      htmlClasses,
      bodyStyle,
      bodyClasses = [`b-${this.constructor.type}`, ...this.owner.bodyClasses, ...Widget.outerCls],
      paperHeight,
      paperWidth,
      html
    } = data, afterPrint = BrowserHelper.isSafari ? "" : "window.removeElement && window.removeElement();", script = !(config == null ? void 0 : config.debug) ? `<script type="text/javascript">window.addEventListener('load', function() { window.print(); ${afterPrint} });<\/script>` : "";
    return TemplateHelper.tpl`
            <!DOCTYPE html>
            <html class="${htmlClasses}" style="width: ${paperWidth}in; height: ${paperHeight}in;">
                <head>
                    <meta content="text/html; charset=UTF-8" http-equiv="Content-Type" />
                    <title>${title}</title>
                    ${styles2.join("")}
                </head>
                <body class="b-print ${bodyClasses.join(" ")}" style="width: ${paperWidth}in; height: ${paperHeight}in; ${bodyStyle}">
                    <div class="b-print-content">
                        ${header && `<div class="b-print-header" style="width: 100%">${header}</div>`}
                        <div class="b-print-body"><div class="b-print-viewport">${html}</div></div>
                        ${footer && `<div class="b-print-footer" style="width: 100%">${footer}</div>`}
                    </div>
                    ${script}
                </body>
            </html>`;
  }
};
Printer._$name = "Printer";

// lib/Calendar/feature/print/printer/CalendarPrinter.js
var bigNumberProperty = {
  configurable: true,
  get: () => Number.MAX_SAFE_INTEGER
};
var sumArrayElements = (sum, r) => sum + r;
var CalendarPrinter = class extends Printer {
  getTotalHeight(client) {
    const { activeView } = client;
    switch (client.mode) {
      case "agenda":
        return activeView.virtualScrollHeight;
      case "day":
        return activeView.dayContentElement.scrollHeight + activeView.alldayRowElement.offsetHeight;
      case "week":
        return activeView.dayContentElement.scrollHeight + activeView.alldayRowElement.offsetHeight;
      case "month":
        if (this.owner.wysiwyg) {
          return activeView.weeksElement.scrollHeight + activeView.weekdaysHeader.offsetHeight + 10;
        }
        break;
      case "year":
        return this.inchToPx(PaperFormat.A4.width);
    }
  }
  getTotalWidth(client) {
    switch (client.mode) {
      case "year":
        return this.inchToPx(PaperFormat.A4.height);
      default:
        return this.inchToPx(PaperFormat.A4.width);
    }
  }
  async prepareComponent(config) {
    const me = this, { owner } = me, { wysiwyg } = owner, shrinkwrapRowHeights = !wysiwyg, { client } = config, { activeView } = client;
    me.exportMeta = {
      title: owner.callback(owner.titleRenderer, owner, [activeView]),
      totalWidth: me.getTotalWidth(client),
      totalHeight: me.getTotalHeight(client),
      subGrids: {}
    };
    if (client.mode === "month" && shrinkwrapRowHeights) {
      Object.defineProperty(activeView, "eventsPerCell", bigNumberProperty);
      Object.defineProperty(activeView, "maxEventsPerCell", bigNumberProperty);
      activeView.doRefresh();
    }
    me.cloneElement(activeView.currentElement);
    if (client.mode === "month") {
      let rowHeights;
      if (shrinkwrapRowHeights) {
        rowHeights = activeView.shrinkwrapRowHeights;
        delete activeView.eventsPerCell;
        delete activeView.maxEventsPerCell;
        activeView.doRefresh();
        me.exportMeta.totalHeight = rowHeights.reduce(sumArrayElements, activeView.weekdaysHeader.offsetHeight);
      }
      activeView.weekElements.forEach((el, i) => {
        const clonedElement = me.element.querySelector(`[data-week="${el.dataset.week}"]`);
        clonedElement.style.minHeight = "";
        if (shrinkwrapRowHeights) {
          clonedElement.style.flex = `0 0 ${rowHeights[i]}px`;
        } else if (wysiwyg) {
          clonedElement.style.flex = `1 0 ${el.offsetHeight}px`;
        } else {
          clonedElement.style.flex = `1 0 ${250 / 6}mm`;
        }
      });
    }
  }
  pxToInch(value2) {
    return (value2 / 96).toFixed(2);
  }
};
__publicField(CalendarPrinter, "$name", "CalendarPrinter");
__publicField(CalendarPrinter, "type", "calendar");
CalendarPrinter.prototype.pagesExtractor = async function* pagesExtractor7(config) {
  const me = this, {
    totalHeight,
    totalWidth,
    title
  } = me.exportMeta;
  yield {
    html: me.pageTpl({
      config,
      title,
      html: me.prepareExportElement(),
      styles: me.stylesheets,
      paperWidth: me.pxToInch(totalWidth),
      paperHeight: me.pxToInch(totalHeight)
    })
  };
};
CalendarPrinter._$name = "CalendarPrinter";

// lib/Calendar/feature/print/Print.js
var emptyArray17 = Object.freeze([]);
var Print3 = class extends CalendarFeature {
  static get configurable() {
    return {
      /**
       * Config for printer.
       * @config {Object}
       * @private
       */
      printer: {
        $config: ["lazy", "nullify"],
        value: {
          type: "calendar"
        }
      },
      /**
       * By default, a {@link Calendar.widget.MonthView} is rendered with the week rows
       * shrinkwrapping their event content. That is, rows with no events are small, and rows
       * containing events are expanded to make all events visible.
       *
       * To duplicate the on-screen appearance in terms of week row heights where empty rows
       * are flexed to consume an equal amount of height, configure this as `true`.
       * @config {Boolean}
       * @default
       */
      wysiwyg: false,
      /**
       * The function to use to create the [&lt;title&gt;](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/title) element for the print document.
       * Defaults to using the {@link Scheduler.view.mixin.Describable#config-descriptionRenderer}
       * of the active view. Note that this does not process HTML source.
       *
       * @config {Function|String}
       * @param {Calendar.widget.mixin.CalendarMixin} source The view which triggered the event.
       * @typings source -> {typeof CalendarMixin}
       * @returns {String}
       * @default
       */
      titleRenderer: (activeView) => activeView.description,
      /**
       * Map of printer classes to use in print feature
       * @config {Map}
       * @private
       * @default
       */
      printers: /* @__PURE__ */ new Map([[CalendarPrinter.type, CalendarPrinter]]),
      /**
       * This config is passed to the Grid print feature. Print dialog is ignored, instead Grid is printed with
       * best default configs.
       * @config {Object}
       * @private
       */
      gridPrintConfig: null
    };
  }
  construct() {
    super.construct(...arguments);
    this.client.print = this.print.bind(this);
  }
  changePrinter(config) {
    const me = this, printersMap = me.printersMap || (me.printersMap = /* @__PURE__ */ new Map());
    if (config) {
      let printer;
      const { type } = config;
      if (printersMap.has(type)) {
        printer = printersMap.get(type);
        printer.setConfig(config);
      } else {
        const printerClass = me.printers.get(type);
        if (!printerClass) {
          throw new Error(`Printer type ${type} is not found. Make sure you've configured it`);
        }
        config = ObjectHelper.clone(config);
        config.owner = me;
        printer = new printerClass(config);
        printer.relayAll(me);
        printersMap.set(type, printer);
      }
      return printer;
    } else {
      printersMap.forEach((printer) => printer.destroy());
    }
  }
  get bodyClasses() {
    if (this.shrinkwrapRowHeights) {
      return ["b-shrinkwrap-row-heights"];
    }
    return emptyArray17;
  }
  /**
   * Prints the current active view of the Calendar
   * @privateparam config A config object for the Printer
   */
  async print(config = {}) {
    var _a5;
    const me = this, {
      client,
      printer
    } = me, iframeId = `${client.id}-print-iframe`;
    if (client.trigger("beforePrint", { config, printer }) !== false) {
      if (client.activeView.isEventList) {
        await client.activeView.print(ObjectHelper.assign({
          exporterType: "multipagevertical",
          useRenderedColumnWidth: true
        }, this.gridPrintConfig || {}));
        client.trigger("print");
      } else {
        config.client = client;
        const pages = await me.printer.export(config);
        if (config.debug) {
          client.trigger("print", pages);
        } else {
          (_a5 = document.getElementById(iframeId)) == null ? void 0 : _a5.remove();
          const iframe = DomHelper.createElement({
            id: iframeId,
            class: "b-calendar-print-iframe",
            tag: "iframe",
            parent: document.body
          });
          iframe.contentWindow.removeElement = () => {
            iframe.remove();
            client.trigger("print", pages);
          };
          iframe.contentWindow.document.write(pages[0].html);
          iframe.contentWindow.document.close();
          return pages;
        }
      }
    }
  }
};
__publicField(Print3, "$name", "Print");
__publicField(Print3, "type", "print");
Print3.initClass();
Print3._$name = "Print";

// lib/Calendar/feature/TimeRanges.js
var genericRenderer = (renderData) => ({
  className: renderData.cls,
  style: renderData.style
});
var defaultRotation = {
  end: "pos",
  start: "neg"
};
var oppositeAlign = {
  end: "start",
  start: "end"
};
var valueFields = Object.entries({
  alignment: "alignment",
  color: "color",
  footer: "footer",
  header: "name",
  rotation: "rotation"
});
var DayZone2 = class extends CalendarZone {
  onBeforeLayoutEvents({ source, context }) {
    const [startDate, endDate] = source.isDayView ? [DateHelper.add(context.cellData.date, source.dayStartMs), DateHelper.add(context.cellData.date, source.dayEndMs)] : [context.cellData.date, context.cellData.tomorrow], ranges = source.getTimeRanges(startDate, endDate);
    if (ranges.length) {
      const { layout } = context, footers = {}, headers = {}, clusters = [], items2 = [];
      let children, item;
      context.timeRangeHeaders = headers;
      context.timeRanges = ranges;
      for (const timeRange of ranges) {
        item = layout.createLayoutItem(timeRange, context);
        item.values = Object.fromEntries(valueFields.map(([name, fieldName]) => [name, timeRange[fieldName]]));
        item.values.rotation = item.values.rotation || defaultRotation[item.values.alignment];
        layout.clusterize(clusters, items2, item, context);
        children = context.dayDomConfig.children.inset;
        children = children.children || (children.children = []);
        children.push(this.renderTimeSpan(context, item, headers, footers));
      }
      if (headers.end || footers.end) {
        context.dayDomConfig.className["b-dayview-inset-after"] = 1;
      }
      if (headers.start || footers.start) {
        context.dayDomConfig.className["b-dayview-inset-before"] = 1;
      }
    }
  }
  renderPart(defaultRenderer, renderer, renderData, part, headerFooter, align, extraData) {
    const isFooter = part === "footer", { record } = renderData, renderInfo = {
      timeRange: record
    };
    let data = renderData, domConfig, extraDom, value2;
    if (typeof part === "string") {
      renderData[part] = data = ObjectHelper.merge({
        part,
        record,
        outer: renderData,
        cls: {
          [`b-cal-timerange-${part}`]: 1
        }
      }, extraData);
      value2 = renderData.values[part];
      if (value2 != null) {
        renderInfo.value = data.value = value2;
      }
    } else {
      extraDom = part;
      part = "outer";
    }
    defaultRenderer = defaultRenderer == null ? void 0 : defaultRenderer[part];
    renderer = renderer == null ? void 0 : renderer[part];
    renderInfo.renderData = data;
    renderInfo.domConfig = extraDom;
    if (renderer) {
      data.style = {};
      const ret = renderer(renderInfo);
      if (typeof ret === "string") {
        extraDom = Object.assign({ html: ret }, extraDom);
      } else if (ObjectHelper.isObject(ret)) {
        domConfig = ret;
      }
    }
    domConfig = domConfig || (defaultRenderer == null ? void 0 : defaultRenderer(renderInfo));
    if (extraDom) {
      domConfig = ObjectHelper.merge(domConfig || {}, extraDom);
    }
    if (headerFooter && domConfig) {
      align = isFooter && oppositeAlign[align] || align;
      headerFooter[align] = (headerFooter[align] || 0) + 1;
    }
    data.domConfig = domConfig;
    return domConfig;
  }
  renderTimeSpan(context, item, headers, footers) {
    const me = this, { owner } = me, { defaultRenderer, renderer } = owner, timeRange = item.eventRecord, sizeSeconds = isNaN(item.end) ? 0 : Math.abs(item.end - item.start), isRange = sizeSeconds > 1, isLine = !isRange, { rtl } = context.layout.owner, { values } = item, { alignment: align, color, rotation } = values, renderData = {
      color,
      values,
      isLine,
      record: timeRange,
      layoutContext: context,
      cls: {
        "b-readonly": timeRange.readOnly,
        "b-rtl": rtl,
        "b-cal-timerange": 1,
        "b-cal-timerange-line": isLine,
        "b-cal-timerange-narrow": isRange && sizeSeconds <= owner.narrowThreshold * 60,
        [`b-cal-timerange-align-${align}`]: align,
        [`b-cal-timerange-rotate-${rotation}`]: isRange && rotation
      }
    };
    isRange && item.height.adjust(0, -1);
    const styles2 = item.getStyles(rtl);
    return DomHelper.normalizeChildren(me.renderPart(defaultRenderer, renderer, renderData, {
      dataset: {
        "timerange-id": timeRange.id,
        btip: isLine && values.header || null
      },
      className: {
        [timeRange.cls]: timeRange.cls
      },
      elementData: {
        timeRange
      },
      style: {
        top: styles2.top,
        height: styles2.height
      },
      children: isRange && {
        header: me.renderPart(defaultRenderer, renderer, renderData, "header", headers, align, {
          cls: {
            [timeRange.iconCls]: timeRange.iconCls
          }
        }),
        body: me.renderPart(defaultRenderer, renderer, renderData, "body"),
        footer: me.renderPart(defaultRenderer, renderer, renderData, "footer", footers, align)
      }
    }));
  }
};
__publicField(DayZone2, "$name", "DayZone");
__publicField(DayZone2, "configurable", {
  viewListeners: {
    beforeLayoutEvents: "onBeforeLayoutEvents"
    // renderEvents       : 'onRenderEvents'
  }
});
var TimeRanges2 = class extends CalendarFeature.mixin(AttachToProjectMixin_default) {
  attachToProject(project) {
    var _a5, _b2;
    super.attachToProject(project);
    this.detachListeners("project");
    (_a5 = project.timeRangeStore) == null ? void 0 : _a5.ion({
      name: "project",
      change: "refresh",
      thisObj: this
    });
    (_b2 = project.resourceTimeRangeStore) == null ? void 0 : _b2.ion({
      name: "project",
      change: "refresh",
      thisObj: this
    });
  }
  changeRenderer(renderer) {
    if (typeof renderer === "function") {
      renderer = {
        header: renderer
      };
    }
    return renderer;
  }
  updateHeaderWidth(width) {
    var _a5, _b2;
    const el = (_a5 = this.owner) == null ? void 0 : _a5.element;
    if (el) {
      el.style.setProperty("--timerange-header-width", (_b2 = LayoutDim.from(width)) == null ? void 0 : _b2.toString());
    }
  }
  refresh() {
    this.client.refresh();
  }
};
__publicField(TimeRanges2, "$name", "TimeRanges");
__publicField(TimeRanges2, "type", "timeRanges");
__publicField(TimeRanges2, "configurable", {
  defaultRenderer: {
    outer({ renderData }) {
      var _a5;
      const ret = genericRenderer(renderData), { color } = renderData;
      if (color) {
        if (DomHelper.isNamedColor(color)) {
          ret.className[`b-cal-color-${color}`] = 1;
        } else {
          (ret.style || (ret.style = {}))["--timerange-color"] = color;
        }
      }
      ret.className["b-cal-timerange-has-header"] = (_a5 = renderData.header) == null ? void 0 : _a5.domConfig;
      return ret;
    },
    body: ({ renderData }) => genericRenderer(renderData),
    header: ({ renderData, value: value2 }) => value2 ? {
      ...genericRenderer(renderData),
      children: [{
        className: {
          "b-cal-timerange-header-text": 1
        },
        text: value2
      }]
    } : null,
    footer: ({ renderData, value: value2 }) => value2 ? {
      ...genericRenderer(renderData),
      children: [{
        className: {
          "b-cal-timerange-footer-text": 1
        },
        text: value2
      }]
    } : null
  },
  /**
   * The number of pixels or proportion of the overall width to allocate for time range headers.
   *
   * Values less than 1 are the fractional proportion of the width (for example, 0.04 is 4% of the width),
   * while values greater than or equal to 1 are a number of pixels.
   * @config {Number}
   * @default
   */
  headerWidth: 40,
  narrowThreshold: 60,
  /**
   * An empty function by default, but provided so that you can override it.
   *
   * This function is called each time a time range is rendered to allow developers to mutate the element metadata,
   * or the CSS classes to be applied to the rendered element.
   *
   * It's called with a {@link #typedef-TimeRangeRenderInfo} object containing the time span record, and a
   * {@link #typedef-TimeRangeRenderData renderData} object which allows you to mutate event metadata such as
   * `cls` and `style`.
   *
   * A non-null return value from the renderer is used as the element body content. A nullish return value results
   * in the default renderer for the element.
   *
   * ```javascript
   *  timeRanges : {
   *      renderer ({ timeRange, renderData }) {
   *          if (timeRange.name === 'Doctors appointment') {
   *              renderData.style.fontWeight = 'bold';
   *              renderData.cls['custom-cls'] = 1;
   *
   *              return 'Special doctors appointment';
   *          }
   *      }
   *  }
   * ```
   * <div class="note">When returning content, be sure to consider how that content should be encoded to avoid XSS
   * (Cross-Site Scripting) attacks. This is especially important when including user-controlled data such as
   * the event's `name`. The function {@link Core.helper.StringHelper#function-encodeHtml-static} as well as
   * {@link Core.helper.StringHelper#function-xss-static} can be helpful in these cases.</div>
   *
   * For example:
   * ```javascript
   *  timeRanges : {
   *      renderer ({ timeRange, renderData }) {
   *          return StringHelper.xss`Special ${timeRange.name}`;
   *      }
   *  }
   * ```
   *
   * For advanced rendering, this config can be a {@link #typedef-TimeRangeRenderer} object with rendering
   * functions for individual elements: `header`, `body`, `footer`, and `outer`. When a function is provided,
   * that is equivalent to passing the `header` renderer. In other words, the above example is equivalent to
   * the following:
   *
   * ```javascript
   *  timeRanges : {
   *      renderer : {
   *          header({ timeRange, renderData }) {
   *              return StringHelper.xss`Special ${timeRange.name}`;
   *          }
   *      }
   *  }
   * ```
   *
   * Note that if the TimeRange has zero duration, it will be rendered as a line, and the `header`, `body` and `footer`
   * renderers will not add any content. The `outer` renderer may mutate the `domConfig` property to affect how
   * the line will be rendered.
   *
   * @config {Function|TimeRangeRenderer} renderer
   * @param {TimeRangeRenderInfo} info An object that contains data about the time span being rendered.
   * @returns {String}
   * @default
   */
  renderer: null,
  zoneTypes: {
    day: DayZone2,
    // also covers WeekView
    resource: DayZone2
  }
});
CalendarFeature.register(TimeRanges2.type, TimeRanges2);
TimeRanges2._$name = "TimeRanges";

// lib/Calendar/feature/WeekExpander.js
var WeekExpander = class extends CalendarFeature {
  static get pluginConfig() {
    return {
      chain: ["render"]
    };
  }
  render() {
    const { client } = this, monthView = this.monthView = client.isMonthView ? client : client.modes.month;
    if (monthView) {
      const { weekRenderer } = this;
      monthView.showWeekColumn = true;
      monthView.weekRenderer = monthView.weekRenderer ? FunctionHelper.createSequence(monthView.weekRenderer, weekRenderer) : weekRenderer;
      monthView.ion({
        weeknumberclick: "onWeekNumberClick",
        thisObj: this,
        // We must get in first so we can veto the switch to week view if we toggle a row.
        prio: 1e4
      });
    }
  }
  weekRenderer(parent, [year, week]) {
    if (!parent.querySelector(".b-week-toggle-tool-wrap")) {
      DomHelper.createElement({
        parent,
        className: "b-week-toggle-tool-wrap",
        children: [{
          className: "b-icon b-week-toggle-tool",
          dataset: {
            btip: this.L("L{WeekExpander.expandTip}")
          }
        }]
      });
    }
  }
  onWeekNumberClick({ domEvent, date: date2 }) {
    const toggleTool = domEvent.target.closest(".b-week-toggle-tool");
    if (toggleTool && !this.disabled) {
      const weekRow = domEvent.target.closest(".b-calendar-row");
      if (weekRow.classList.contains("b-shrinkwrapped")) {
        this.monthView.flexWeekRow(date2, true);
        toggleTool.dataset.btip = this.L("L{expandTip}");
      } else if (weekRow.classList.contains("b-has-overflow")) {
        this.monthView.shrinkwrapWeekRow(date2);
        toggleTool.dataset.btip = this.L("L{collapseTip}");
      }
      return false;
    }
  }
  updateDisabled(disabled, was) {
    super.updateDisabled(disabled, was);
    this.client.element.classList.toggle("b-weekexpander", !disabled);
  }
};
__publicField(WeekExpander, "$name", "WeekExpander");
__publicField(WeekExpander, "type", "weekExpander");
WeekExpander.initClass();
WeekExpander._$name = "WeekExpander";

// lib/Calendar/widget/MonthAgendaView.js
var _MonthAgendaView = class _MonthAgendaView extends CalendarDatePicker.mixin(CalendarMixin_default) {
  checkAutoCreateGesture(domEvent) {
    if (this.agenda.eventContentElement.contains(domEvent.target)) {
      super.checkAutoCreateGesture(...arguments);
    }
  }
  get description() {
    const { date: date2 } = this.agenda, today = DateHelper.clearTime(/* @__PURE__ */ new Date());
    if (!(today - date2)) {
      return this.L("L{Calendar.Today}");
    }
    if (!DateHelper.diff(date2, DateHelper.add(today, 1, "d"))) {
      return this.L("L{Calendar.Tomorrow}");
    }
    return super.description;
  }
  agendaHeaderRenderer(date2) {
  }
  changeShiftIncrement(shiftIncrement) {
    return Duration.from(shiftIncrement);
  }
  get stepUnit() {
    const { shiftIncrement } = this;
    if (shiftIncrement) {
      return shiftIncrement.magnitude === 1 ? DateHelper.getLocalizedNameOfUnit(shiftIncrement.unit) : `${shiftIncrement.magnitude} ${DateHelper.getLocalizedNameOfUnit(shiftIncrement.unit, true)}`;
    }
    return this.agenda.stepUnit;
  }
  getEventElement() {
    return this.agenda.getEventElement(...arguments);
  }
  next() {
    const {
      date: date2,
      animateTimeShift
    } = this, newDate = DateHelper.add(date2, Duration.from(this.shiftIncrement));
    this.animateTimeShift = newDate.getMonth() !== date2.getMonth();
    this.date = newDate;
    this.animateTimeShift = animateTimeShift;
  }
  previous() {
    const {
      date: date2,
      animateTimeShift
    } = this, newDate = DateHelper.add(date2, Duration.from(this.shiftIncrement).negate());
    this.animateTimeShift = newDate.getMonth() !== date2.getMonth();
    this.date = newDate;
    this.animateTimeShift = animateTimeShift;
  }
  changeAgenda(agenda, was) {
    was && this.remove(was);
    if (agenda) {
      const me = this, { date: date2 } = me;
      agenda = _MonthAgendaView.reconfigure(was, _MonthAgendaView.mergeConfigs({
        date: date2,
        flex: 1,
        project: me.project,
        title: me.callback(me.agendaHeaderRenderer, me, [date2]),
        ref: "agenda",
        cls: {
          "b-monthagenda-agenda": 1
        },
        range: "day",
        settingsButton: null,
        alwaysShowCurrentDate: true,
        consumeGridPointerEvents: false,
        defaultScrollOptions: {
          block: "start"
        },
        internalListeners: {
          topDateChange: "onAgendaTopDateChange",
          rangeChange: "onAgendaRangeChange",
          thisObj: me
        }
      }, agenda), me);
      me.add(agenda);
      me.eventSorter = agenda.eventSorter;
      const { classList } = agenda.element;
      classList.remove("b-slide-vertical");
      classList.add("b-slide-horizontal");
    } else {
      was == null ? void 0 : was.destroy();
    }
    return agenda;
  }
  // Keep our date synced with the stuck top date of the Agenda
  onAgendaTopDateChange({ source, date: date2 }) {
    if (!source.scrollable.scrollAnimation) {
      source.preventScroll = true;
      this.date = date2;
      source.preventScroll = false;
    }
  }
  // When the Agenda changes range, keep our date at the top if possible
  onAgendaRangeChange({ source }) {
    if (source.isAgendaView && source.store.count > 1) {
      const date2 = new Date(Math.min(Math.max(this.date, source.store.first.date), source.store.last.date));
      source.scrollTo(date2, {
        animate: false,
        block: "start"
      });
    }
  }
  createCellData() {
    return this.agenda.createCellData(...arguments);
  }
  updateDate(date2, was) {
    const { agenda } = this;
    this.selection = date2;
    super.updateDate(date2);
    if (agenda) {
      agenda.date = date2;
      agenda.title = this.callback(this.agendaHeaderRenderer, this, [date2]);
      if (agenda.duration === 1) {
        DomHelper.slideIn(agenda.element, date2 - was);
      }
    }
  }
  getDateFromDomEvent(e) {
    return super.getDateFromDomEvent(e) || this.agenda.getDateFromDomEvent(e);
  }
};
__publicField(_MonthAgendaView, "$name", "MonthAgendaView");
__publicField(_MonthAgendaView, "type", "monthagenda");
// Delayables are not inherited, so we need to restore the delayables from DatePicker
__publicField(_MonthAgendaView, "delayable", {
  refresh: "raf"
});
__publicField(_MonthAgendaView, "configurable", {
  // region Hidden configs
  /**
   * @hideconfigs shortEventDuration, eventHeight, eventSpacing
   */
  // endRegion
  title: "L{MonthView.Month}",
  showEvents: "dots",
  tbar: null,
  animateTimeShift: false,
  eventHeight: "auto",
  trapFocus: false,
  focusable: true,
  /**
   * The time range to move by when {@link #function-next} or {@link #function-previous} is called.
   *
   * Valid values are:
   * - day
   * - week
   * - month
   * - year
   * - decade
   *
   * This may also be specified as a duration with a magnitude part and a unit part. For
   * example `'1m'` would mean one month.
   *
   * In this view, probably the choices should be:
   * - '1 day'
   * - '1 month'
   *
   * @prp {DurationConfig}
   * @accepts {String|DurationConfig}
   * @default '1 day'
   */
  shiftIncrement: { unit: "day", magnitude: 1 },
  /**
   * A {@link Core.helper.DateHelper date formatting string} which produces a title for the agenda part of the view.
   * @config {String}
   */
  agendaDateFormat: "dddd DD MMMM YYYY",
  /**
   * A function or the name of a function in the ownership hierarchy which, when passed the active date produces a
   * title for the agenda part of the view. Defaults to having *no header*.
   * @config {Function|String}
   * @param {Date} date The date which the Agenda part of the view is displaying.
   * @returns {String} The content of the agenda header below the month view.
   */
  agendHeaderRenderer: null,
  /**
   * A config object to reconfigure the default child view which displays the selected date's events.
   *
   * By default, an {@link Calendar.widget.AgendaView} with a {@link Calendar.widget.AgendaView#config-range}
   * of `'1 day'` is used. You may specify other `type`s such as `'dayview'` or `'dayagenda'`:
   *
   * ```javascript
   * modes : {
   *     month : {
   *         // Our month view shows a MonthAgenda view on phones
   *         // And when it does, the child "agenda" is a full DayView
   *         type   : matchMedia('(max-width : 600)').matches ? 'monthagenda' : 'monthview',
   *         agenda : {
   *             type         : 'dayview',
   *             dayStartTime : 8
   *         }
   *     }
   * }
   * ```
   *
   * @prp {Calendar.widget.AgendaView|Calendar.widget.DayView|Calendar.widget.DayAgendaView|Calendar.widget.EventList}
   * @accepts {AgendaViewConfig|DayViewConfig|DayAgendaViewConfig|EventListConfig|Object}
   * @param {'agendaview'|'dayview'|'dayagenda'|'eventlist'} type The type of view to display the day's events below the month.
   * @default
   */
  agenda: {
    type: "agendaview"
  }
});
var MonthAgendaView = _MonthAgendaView;
MonthAgendaView.initClass();
MonthAgendaView._$name = "MonthAgendaView";
export {
  AbstractAssignmentStoreMixin,
  AbstractCalendarManagerStoreMixin,
  AbstractCalendarMixin,
  AbstractCrudManager,
  AbstractCrudManagerMixin_default as AbstractCrudManagerMixin,
  AbstractCrudManagerValidation_default as AbstractCrudManagerValidation,
  AbstractDateRange_default as AbstractDateRange,
  AbstractDependencyStoreMixin,
  AbstractEventStoreMixin,
  AbstractHasAssignmentsMixin,
  AbstractPartOfProjectGenericMixin,
  AbstractPartOfProjectModelMixin,
  AbstractPartOfProjectStoreMixin,
  AbstractRecurrenceIterator,
  AbstractResourceStoreMixin,
  AbstractTimeRanges,
  ActionBase,
  ActionColumn,
  AddAction,
  AgendaColumn,
  AgendaView,
  AggregateColumn,
  AjaxHelper,
  AjaxStore,
  AjaxStoreLazyLoad as AjaxStoreLazyLoadPlugin,
  AjaxTransport_default as AjaxTransport,
  Animator,
  ArrayDataField,
  ArrayHelper,
  AssignmentModel,
  AssignmentModelMixin_default as AssignmentModelMixin,
  AssignmentStore,
  AssignmentStoreMixin_default as AssignmentStoreMixin,
  AsyncHelper,
  AttachToProjectMixin_default as AttachToProjectMixin,
  AvatarRendering,
  Badge_default as Badge,
  Bag,
  Bar,
  Base,
  BooleanCombo,
  BooleanDataField,
  Box,
  BrowserHelper,
  Button,
  ButtonGroup,
  CI,
  CIFromSetOrArrayOrValue,
  CSSHelper,
  Calendar,
  CalendarCache,
  CalendarCacheInterval,
  CalendarCacheIntervalMultiple,
  CalendarCacheMultiple,
  CalendarCacheSingle,
  CalendarDatePicker,
  CalendarDrag,
  CalendarFeature,
  CalendarIntervalMixin,
  CalendarIntervalStore,
  CalendarIntervalType,
  CalendarIteratorResult,
  CalendarMixin_default as CalendarMixin,
  CalendarPanel,
  CalendarRow,
  CalendarStores_default as CalendarStores,
  Card,
  Carousel,
  CellCopyPaste,
  CellEdit,
  CellMenu,
  CellTooltip,
  CheckColumn,
  Checkbox,
  CheckboxGroup,
  ChecklistFilterCombo,
  ChipView,
  Base2 as ChronoMixinBase,
  ClickRepeater,
  Clipboardable_default as Clipboardable,
  ClockTemplate,
  CodeEditor,
  CollapseTool,
  Collection,
  CollectionFilter,
  CollectionSorter,
  ColorColumn,
  ColorField,
  ColorPicker,
  Column,
  ColumnAutoWidth,
  ColumnDragToolbar,
  ColumnLines,
  ColumnPicker,
  ColumnRename,
  ColumnReorder,
  ColumnResize,
  ColumnStore,
  Combo,
  ComboModel,
  Config,
  ConfirmationBar,
  ConstraintIntervalSide,
  ConstraintType,
  Container,
  ContextMenuBase,
  CopyPasteBase,
  CoreAssignmentMixin,
  CoreAssignmentStoreMixin,
  CoreCalendarManagerStoreMixin,
  CoreCalendarMixin,
  CoreDependencyMixin,
  CoreDependencyStoreMixin,
  CoreEventMixin,
  CoreEventStoreMixin,
  CoreHasAssignmentsMixin,
  CoreHasDependenciesMixin,
  CorePartOfProjectGenericMixin,
  CorePartOfProjectModelMixin,
  CorePartOfProjectStoreMixin,
  CoreResourceMixin,
  CoreResourceStoreMixin,
  CrudManager2 as CrudManager,
  CrudManagerView_default as CrudManagerView,
  DailyRecurrenceIterator,
  DataField,
  DataGenerator,
  DateColumn,
  DateDataField,
  DateField,
  DateHelper,
  DatePicker,
  DateRangeField,
  DateRangeOwner_default as DateRangeOwner,
  DateRangePicker,
  DateStoreLazyLoadPlugin,
  DateTimeField,
  DayAgendaView,
  DayButtons,
  DayCellCollecter_default as DayCellCollecter,
  DayCellRenderer_default as DayCellRenderer,
  DayLayout,
  DayResourceCalendarRow,
  DayResourceView,
  DaySelectable_default as DaySelectable,
  DaySelector,
  DayTime,
  DayView,
  Delayable_default as Delayable,
  DelayableWrapper2 as DelayableWrapper,
  DelayedRecordsRendering_default as DelayedRecordsRendering,
  DemoBot,
  DemoCodeEditor,
  DemoHeader,
  Dependencies,
  DependenciesCalendar,
  DependencyBaseModel,
  DependencyCreation_default as DependencyCreation,
  DependencyEdit,
  DependencyMenu,
  DependencyModel,
  DependencyStore,
  DependencyStoreMixin_default as DependencyStoreMixin,
  DependencyTooltip_default as DependencyTooltip,
  DependencyType,
  DependencyValidationResult,
  Describable_default as Describable,
  Direction,
  DisplayField,
  DomClassList,
  DomDataStore,
  DomHelper,
  DomSync,
  DragBase,
  DragContext,
  DragCreateBase,
  DragHelper,
  DragProxy,
  DragTipProxy,
  Draggable_default as Draggable,
  Droppable_default as Droppable,
  Duration,
  DurationColumn,
  DurationField,
  DurationUnitDataField,
  DynamicObject,
  EdgeInclusion,
  EditBase,
  Editor,
  ElementResizeHelper,
  ElementVisitor,
  EventBuffer,
  EventColorColumn,
  EventColorField,
  EventColorPicker,
  EventColumn,
  EventCopyPaste2 as EventCopyPaste,
  EventDayIndex,
  EventDrag,
  EventDragCreate,
  EventDragSelect,
  EventEdit2 as EventEdit,
  EventEditor2 as EventEditor,
  EventFilter,
  EventHelper,
  EventList,
  EventMenu2 as EventMenu,
  EventModel,
  EventModelMixin_default as EventModelMixin,
  EventNavigation_default2 as EventNavigation,
  EventNonWorkingTime,
  EventRenderer_default as EventRenderer,
  EventResize,
  EventSelection_default2 as EventSelection,
  EventSlots,
  EventSorter,
  EventStore,
  EventStoreMixin_default as EventStoreMixin,
  EventTip,
  EventTooltip2 as EventTooltip,
  Events_default as Events,
  ExcelExporter3 as ExcelExporter,
  ExportDialog,
  ExportOrientationCombo,
  ExportRowsCombo,
  Exporter,
  ExporterBase,
  ExternalEventSource,
  Factoryable_default as Factoryable,
  Featureable_default as Featureable,
  Fencible_default as Fencible,
  Field,
  FieldContainer,
  FieldFilterPicker,
  FieldFilterPickerGroup,
  FieldSet,
  FileDrop,
  FileField,
  FileFormat,
  FilePicker,
  FillHandle,
  Filter,
  FilterBar,
  FilterField,
  Finalizable_default as Finalizable,
  Fit,
  FluidDayLayout,
  Footer,
  Formatter,
  FormulaField_default as FormulaField,
  FormulaProvider,
  Fullscreen,
  FunctionHelper,
  GetEventsMixin_default as GetEventsMixin,
  GlobalEvents_default as GlobalEvents,
  Grid,
  GridBase,
  GridEditBase,
  GridElementEvents_default as GridElementEvents,
  ExcelExporter as GridExcelExporter,
  GridFeatureManager,
  GridFeatures_default as GridFeatures,
  GridFieldFilterPicker,
  GridFieldFilterPickerGroup,
  GroupSummary as GridGroupSummary,
  GridLocation,
  LockRows as GridLockRows,
  MultiPageExporter as GridMultiPageExporter,
  MultiPageVerticalExporter as GridMultiPageVerticalExporter,
  PdfExport as GridPdfExport,
  Print as GridPrint,
  GridResponsive_default as GridResponsive,
  GridRowModel,
  RowReorder as GridRowReorder,
  RowResize as GridRowResize,
  GridSelection_default as GridSelection,
  SinglePageExporter as GridSinglePageExporter,
  Split as GridSplit,
  GridState_default as GridState,
  GridSubGrids_default as GridSubGrids,
  Summary as GridSummary,
  Group,
  GroupBar,
  GroupSummary2 as GroupSummary,
  Header,
  HeaderMenu,
  HeaderZoom,
  Helpers_default as Helpers,
  Hint,
  HintFlow,
  Histogram,
  HorizontalLayoutPack,
  HorizontalLayoutStack,
  HorizontalRendering,
  HorizontalTimeAxis,
  Hoverable_default as Hoverable,
  IdHelper,
  Identifiable_default as Identifiable,
  IndexPosition,
  Axis as InfinityAxis,
  InfinityScrollable_default as InfinityScrollable,
  InfinityScroller,
  InsertAction,
  InsertChildAction,
  InstancePlugin,
  IntegerDataField,
  IntervalCache,
  IntervalSorterFn,
  JsonEncoder_default as JsonEncoder,
  KeyMap_default as KeyMap,
  Label,
  Labelable_default as Labelable,
  Labels,
  Layout,
  LayoutDim,
  LazyLoadCrudManager_default as LazyLoadCrudManager,
  LazyLoadView_default as LazyLoadView,
  List,
  LoadMaskable_default as LoadMaskable,
  LoadOnDemand,
  LocaleHelper,
  LocaleManager_default as LocaleManager,
  Localizable_default as Localizable,
  LockRows2 as LockRows,
  MAX_DATE2 as MAX_DATE,
  MI,
  MIN_DATE2 as MIN_DATE,
  Mask,
  Menu,
  MenuItem,
  MergeCells,
  MessageDialog_default as MessageDialog,
  Minifiable_default as Minifiable,
  ModeSelector,
  Model,
  ModelDataField,
  ModelLink_default as ModelLink,
  ModelStm_default as ModelStm,
  Month,
  MonthAgendaView,
  MonthPicker,
  MonthView,
  MonthlyRecurrenceIterator,
  MultiDatePicker,
  MultiPageExporter2 as MultiPageExporter,
  MultiPageVerticalExporter2 as MultiPageVerticalExporter,
  Navigator,
  NonWorkingTime,
  NonWorkingTimeMixin_default as NonWorkingTimeMixin,
  NumberColumn,
  NumberDataField,
  NumberField,
  NumberFormat,
  ObjectDataField,
  ObjectHelper,
  Objects,
  Orientation,
  OverflowPopup,
  Override,
  PackMixin_default as PackMixin,
  PagingToolbar,
  Pan,
  Panel,
  PanelCollapser,
  PanelCollapserOverlay,
  PaperFormat,
  Parser_default as Parser,
  PartOfBaseProject_default as PartOfBaseProject,
  PartOfProject_default as PartOfProject,
  PasswordField,
  PdfExport2 as PdfExport,
  PercentColumn,
  PickerField,
  Pluggable_default as Pluggable,
  Point,
  Popup,
  pm as PresetManager,
  PresetStore,
  Print3 as Print,
  PrintMixin_default as PrintMixin,
  ProjectCombo,
  ProjectConstraintResolution,
  ProjectConsumer_default as ProjectConsumer,
  ProjectCrudManager_default as ProjectCrudManager,
  ProjectModel2 as ProjectModel,
  ProjectModelCommon_default as ProjectModelCommon,
  ProjectModelMixin_default as ProjectModelMixin,
  ProjectModelTimeZoneMixin_default as ProjectModelTimeZoneMixin,
  ProjectType,
  Promissory,
  QuickFind,
  RTL_default as RTL,
  Radio,
  RadioGroup,
  RandomGenerator,
  RangeMenu,
  RatingColumn,
  Rectangle,
  RectangularPathFinder,
  RecurrenceCombo,
  RecurrenceConfirmationPopup,
  RecurrenceDayRuleEncoder,
  RecurrenceDaysButtonGroup,
  RecurrenceDaysCombo,
  RecurrenceEditor,
  RecurrenceEditorPanel,
  RecurrenceFrequencyCombo,
  RecurrenceLegend,
  RecurrenceLegendButton,
  RecurrenceModel,
  RecurrenceMonthDaysButtonGroup,
  RecurrenceMonthsButtonGroup,
  RecurrencePositionsCombo,
  RecurrenceStopConditionCombo,
  RecurringEventEdit_default as RecurringEventEdit,
  RecurringEvents_default as RecurringEvents,
  RecurringEventsMixin_default as RecurringEventsMixin,
  RecurringTimeSpan_default as RecurringTimeSpan,
  RecurringTimeSpansMixin_default as RecurringTimeSpansMixin,
  RegionResize,
  RemoveAction,
  RemoveAllAction,
  RemoveChildAction,
  Resizable_default as Resizable,
  ResizeHelper,
  ResizeMonitor,
  ResourceCollapseColumn,
  ResourceCombo,
  ResourceFilter,
  ResourceHeader,
  ResourceInfoColumn,
  ResourceMenu,
  ResourceModel,
  ResourceModelMixin_default as ResourceModelMixin,
  ResourceStore,
  ResourceStoreMixin_default as ResourceStoreMixin,
  ResourceTickModel,
  ResourceTickStore,
  ResourceTimeRangeModel,
  ResourceTimeRangeStore,
  ResourceTimeRanges,
  ResourceTimeRangesBase,
  ResourceView,
  Responsive_default as Responsive,
  RevisionException,
  Ripple,
  Rotatable_default as Rotatable,
  Row,
  RowCopyPaste,
  RowEdit,
  RowExpander,
  RowManager,
  RowNumberColumn,
  RowReorder2 as RowReorder,
  RowResize2 as RowResize,
  RowsRange,
  Scale,
  ScaleColumn,
  ScheduleContext,
  ScheduleMenu2 as ScheduleMenu,
  ScheduleRange,
  ScheduleRangeCombo,
  ScheduleTableExporter,
  ScheduleTooltip2 as ScheduleTooltip,
  Scheduler,
  SchedulerBase,
  SchedulerCoreEvent,
  SchedulerCoreProjectMixin,
  CrudManager as SchedulerCrudManager,
  SchedulerDatePicker,
  SchedulerDom_default as SchedulerDom,
  SchedulerDomEvents_default as SchedulerDomEvents,
  EventCopyPaste as SchedulerEventCopyPaste,
  EventEdit as SchedulerEventEdit,
  EventEditor as SchedulerEventEditor,
  EventMenu as SchedulerEventMenu,
  EventNavigation_default as SchedulerEventNavigation,
  SchedulerEventRendering_default as SchedulerEventRendering,
  EventSelection_default as SchedulerEventSelection,
  EventTooltip as SchedulerEventTooltip,
  ExcelExporter2 as SchedulerExcelExporter,
  SchedulerExportDialog,
  Print2 as SchedulerPrint,
  ProjectModel as SchedulerProjectModel,
  SchedulerRegions_default as SchedulerRegions,
  SchedulerResourceRendering_default as SchedulerResourceRendering,
  ScheduleMenu as SchedulerScheduleMenu,
  ScheduleTooltip as SchedulerScheduleTooltip,
  SchedulerScroll_default as SchedulerScroll,
  SchedulerState_default as SchedulerState,
  SchedulerStores_default as SchedulerStores,
  TimeRangeModel as SchedulerTimeRangeModel,
  TimeRangeStore as SchedulerTimeRangeStore,
  TimeRanges as SchedulerTimeRanges,
  SchedulingMode,
  ScrollButtons,
  ScrollManager,
  Scroller,
  Search,
  Sidebar,
  SimpleEventEdit,
  SinglePageExporter2 as SinglePageExporter,
  SlideToggle,
  Slider,
  Sort,
  SortedMap,
  Split2 as Split,
  Splitter,
  StartedTaskScheduling,
  State_default as State,
  StateBase,
  StateProvider,
  StateStorage,
  StateTrackingManager,
  StickyCells,
  StickyEvents,
  Store,
  StoreCRUD_default as StoreCRUD,
  StoreChained_default as StoreChained,
  StoreChanges_default as StoreChanges,
  StoreDataField,
  StoreFilter_default as StoreFilter,
  StoreGroup_default as StoreGroup,
  StoreLazyLoadPlugin,
  StorePaging_default as StorePaging,
  StoreProxy_default as StoreProxy,
  StoreRelation_default as StoreRelation,
  StoreSearch_default as StoreSearch,
  StoreSort_default as StoreSort,
  StoreState_default as StoreState,
  StoreStm_default as StoreStm,
  StoreSum_default as StoreSum,
  StoreSync_default as StoreSync,
  StoreTimeZonePlugin,
  StoreTree_default as StoreTree,
  StringDataField,
  StringHelper,
  Stripe,
  Styleable_default as Styleable,
  SubGrid,
  Summary2 as Summary,
  Tab,
  TabBar,
  TabPanel,
  TableExporter,
  TaskEditStm_default as TaskEditStm,
  TaskEditTransactional_default as TaskEditTransactional,
  TemplateColumn,
  TemplateHelper,
  TextAreaField,
  TextAreaPickerField,
  TextField,
  TickCells,
  TimeAxis,
  TimeAxisColumn,
  TimeAxisHeaderMenu,
  TimeAxisSubGrid,
  TimeAxisViewModel,
  TimeColumn,
  TimeField,
  TimePicker,
  TimeRangeModel2 as TimeRangeModel,
  TimeRangeStore2 as TimeRangeStore,
  TimeRanges2 as TimeRanges,
  TimeSelection,
  TimeSpan,
  TimeSpanMenuBase,
  TimeUnit,
  TimeZoneHelper,
  TimeZonedDatesMixin_default as TimeZonedDatesMixin,
  TimelineBase,
  TimelineDateMapper_default as TimelineDateMapper,
  TimelineDomEvents_default as TimelineDomEvents,
  TimelineEventRendering_default as TimelineEventRendering,
  TimelineHistogram,
  TimelineHistogramBase,
  TimelineHistogramGrouping_default as TimelineHistogramGrouping,
  TimelineHistogramScaleColumn_default as TimelineHistogramScaleColumn,
  TimelineScroll_default as TimelineScroll,
  TimelineState_default as TimelineState,
  TimelineSummary,
  TimelineViewPresets_default as TimelineViewPresets,
  TimelineZoomable_default as TimelineZoomable,
  Toast,
  Tool,
  Toolable_default as Toolable,
  Toolbar,
  Tooltip,
  TooltipBase,
  Transaction,
  TransactionalFeature_default as TransactionalFeature,
  TransactionalFeatureMixin_default as TransactionalFeatureMixin,
  TreatInconsistentIntervals,
  Tree,
  TreeColumn,
  TreeCombo,
  TreeGrid,
  TreeGroup,
  TreeNode_default as TreeNode,
  TreeSummary,
  UndoRedo,
  UndoRedoBase,
  UnspecifiedTimeIntervalModel,
  UpdateAction,
  VALUE_COUNT,
  VBox,
  Validatable_default as Validatable,
  VersionHelper,
  VerticalRendering,
  VerticalTimeAxisColumn,
  ViewPreset,
  ViewPresetCombo,
  WalkHelper,
  Wbs,
  WeekExpander,
  WeekView,
  WeeklyRecurrenceIterator,
  Widget,
  WidgetColumn,
  WidgetHelper,
  WriteExcelFileProvider,
  XMLHelper,
  XlsProviderBase,
  YearPicker,
  YearView,
  YearlyRecurrenceIterator,
  binarySearch,
  combineCalendars,
  delay,
  format,
  isDateFinite,
  isEqualEffectiveDirection,
  isNotNumber,
  later,
  stripDuplicates
};
//# sourceMappingURL=calendar.module.js.map
